import * as PIXI from 'pixi.js';
import { Stage, Group, Layer as Layer$1 } from '@pixi/layers';
import { InteractionManager, InteractionTrackingData } from '@pixi/interaction';
import { Viewport } from 'pixi-viewport';

var domain;

// This constructor is used to store event handlers. Instantiating this is
// faster than explicitly calling `Object.create(null)` to get a "clean" empty
// object (tested with v8 v4.9).
function EventHandlers() {}
EventHandlers.prototype = Object.create(null);

function EventEmitter() {
  EventEmitter.init.call(this);
}

// nodejs oddity
// require('events') === require('events').EventEmitter
EventEmitter.EventEmitter = EventEmitter;

EventEmitter.usingDomains = false;

EventEmitter.prototype.domain = undefined;
EventEmitter.prototype._events = undefined;
EventEmitter.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
EventEmitter.defaultMaxListeners = 10;

EventEmitter.init = function() {
  this.domain = null;
  if (EventEmitter.usingDomains) {
    // if there is an active domain, then attach to it.
    if (domain.active ) ;
  }

  if (!this._events || this._events === Object.getPrototypeOf(this)._events) {
    this._events = new EventHandlers();
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || isNaN(n))
    throw new TypeError('"n" argument must be a positive number');
  this._maxListeners = n;
  return this;
};

function $getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter.prototype.getMaxListeners = function getMaxListeners() {
  return $getMaxListeners(this);
};

// These standalone emit* functions are used to optimize calling of event
// handlers for fast cases because emit() itself often has a variable number of
// arguments and can be deoptimized because of that. These functions always have
// the same number of arguments and thus do not get deoptimized, so the code
// inside them can execute faster.
function emitNone(handler, isFn, self) {
  if (isFn)
    handler.call(self);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self);
  }
}
function emitOne(handler, isFn, self, arg1) {
  if (isFn)
    handler.call(self, arg1);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1);
  }
}
function emitTwo(handler, isFn, self, arg1, arg2) {
  if (isFn)
    handler.call(self, arg1, arg2);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1, arg2);
  }
}
function emitThree(handler, isFn, self, arg1, arg2, arg3) {
  if (isFn)
    handler.call(self, arg1, arg2, arg3);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1, arg2, arg3);
  }
}

function emitMany(handler, isFn, self, args) {
  if (isFn)
    handler.apply(self, args);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].apply(self, args);
  }
}

EventEmitter.prototype.emit = function emit(type) {
  var er, handler, len, args, i, events, domain;
  var doError = (type === 'error');

  events = this._events;
  if (events)
    doError = (doError && events.error == null);
  else if (!doError)
    return false;

  domain = this.domain;

  // If there is no 'error' event listener then throw.
  if (doError) {
    er = arguments[1];
    if (domain) {
      if (!er)
        er = new Error('Uncaught, unspecified "error" event');
      er.domainEmitter = this;
      er.domain = domain;
      er.domainThrown = false;
      domain.emit('error', er);
    } else if (er instanceof Error) {
      throw er; // Unhandled 'error' event
    } else {
      // At least give some kind of context to the user
      var err = new Error('Uncaught, unspecified "error" event. (' + er + ')');
      err.context = er;
      throw err;
    }
    return false;
  }

  handler = events[type];

  if (!handler)
    return false;

  var isFn = typeof handler === 'function';
  len = arguments.length;
  switch (len) {
    // fast cases
    case 1:
      emitNone(handler, isFn, this);
      break;
    case 2:
      emitOne(handler, isFn, this, arguments[1]);
      break;
    case 3:
      emitTwo(handler, isFn, this, arguments[1], arguments[2]);
      break;
    case 4:
      emitThree(handler, isFn, this, arguments[1], arguments[2], arguments[3]);
      break;
    // slower
    default:
      args = new Array(len - 1);
      for (i = 1; i < len; i++)
        args[i - 1] = arguments[i];
      emitMany(handler, isFn, this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  if (typeof listener !== 'function')
    throw new TypeError('"listener" argument must be a function');

  events = target._events;
  if (!events) {
    events = target._events = new EventHandlers();
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (!existing) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] = prepend ? [listener, existing] :
                                          [existing, listener];
    } else {
      // If we've already got an array, just append.
      if (prepend) {
        existing.unshift(listener);
      } else {
        existing.push(listener);
      }
    }

    // Check for listener leak
    if (!existing.warned) {
      m = $getMaxListeners(target);
      if (m && m > 0 && existing.length > m) {
        existing.warned = true;
        var w = new Error('Possible EventEmitter memory leak detected. ' +
                            existing.length + ' ' + type + ' listeners added. ' +
                            'Use emitter.setMaxListeners() to increase limit');
        w.name = 'MaxListenersExceededWarning';
        w.emitter = target;
        w.type = type;
        w.count = existing.length;
        emitWarning(w);
      }
    }
  }

  return target;
}
function emitWarning(e) {
  typeof console.warn === 'function' ? console.warn(e) : console.log(e);
}
EventEmitter.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter.prototype.on = EventEmitter.prototype.addListener;

EventEmitter.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function _onceWrap(target, type, listener) {
  var fired = false;
  function g() {
    target.removeListener(type, g);
    if (!fired) {
      fired = true;
      listener.apply(target, arguments);
    }
  }
  g.listener = listener;
  return g;
}

EventEmitter.prototype.once = function once(type, listener) {
  if (typeof listener !== 'function')
    throw new TypeError('"listener" argument must be a function');
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      if (typeof listener !== 'function')
        throw new TypeError('"listener" argument must be a function');
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// emits a 'removeListener' event iff the listener was removed
EventEmitter.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      if (typeof listener !== 'function')
        throw new TypeError('"listener" argument must be a function');

      events = this._events;
      if (!events)
        return this;

      list = events[type];
      if (!list)
        return this;

      if (list === listener || (list.listener && list.listener === listener)) {
        if (--this._eventsCount === 0)
          this._events = new EventHandlers();
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length; i-- > 0;) {
          if (list[i] === listener ||
              (list[i].listener && list[i].listener === listener)) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (list.length === 1) {
          list[0] = undefined;
          if (--this._eventsCount === 0) {
            this._events = new EventHandlers();
            return this;
          } else {
            delete events[type];
          }
        } else {
          spliceOne(list, position);
        }

        if (events.removeListener)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };

EventEmitter.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events;

      events = this._events;
      if (!events)
        return this;

      // not listening for removeListener, no need to emit
      if (!events.removeListener) {
        if (arguments.length === 0) {
          this._events = new EventHandlers();
          this._eventsCount = 0;
        } else if (events[type]) {
          if (--this._eventsCount === 0)
            this._events = new EventHandlers();
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        for (var i = 0, key; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = new EventHandlers();
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners) {
        // LIFO order
        do {
          this.removeListener(type, listeners[listeners.length - 1]);
        } while (listeners[0]);
      }

      return this;
    };

EventEmitter.prototype.listeners = function listeners(type) {
  var evlistener;
  var ret;
  var events = this._events;

  if (!events)
    ret = [];
  else {
    evlistener = events[type];
    if (!evlistener)
      ret = [];
    else if (typeof evlistener === 'function')
      ret = [evlistener.listener || evlistener];
    else
      ret = unwrapListeners(evlistener);
  }

  return ret;
};

EventEmitter.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount$1.call(emitter, type);
  }
};

EventEmitter.prototype.listenerCount = listenerCount$1;
function listenerCount$1(type) {
  var events = this._events;

  if (events) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? Reflect.ownKeys(this._events) : [];
};

// About 1.5x faster than the two-arg version of Array#splice().
function spliceOne(list, index) {
  for (var i = index, k = i + 1, n = list.length; k < n; i += 1, k += 1)
    list[i] = list[k];
  list.pop();
}

function arrayClone(arr, i) {
  var copy = new Array(i);
  while (i--)
    copy[i] = arr[i];
  return copy;
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

var DrawingProperties;
(function (DrawingProperties) {
    DrawingProperties["Net"] = "net";
    DrawingProperties["ObjectId"] = "objectid";
    DrawingProperties["Component"] = "component";
    DrawingProperties["Value"] = "value";
    DrawingProperties["Pin"] = "pin";
    DrawingProperties["Pad"] = "pad";
})(DrawingProperties || (DrawingProperties = {}));
/**
 * Builds the compound key used to identify a specific pin or pad graphic in the renderer's property
 * maps. Both parts are percent-encoded so that any characters (including `/`) in either ref are
 * preserved unambiguously and the `/` separator is always unique.
 *
 * @param moduleRef - The unique module ref (e.g. `"R1"`, `"U1A"`, `"Sheet1/U1"`).
 * @param pinRef    - The pin or pad ref within that module (e.g. `"1"`, `"A2"`).
 * @returns The compound key `"encodedModuleRef/encodedPinRef"`.
 */
function pinPropertyKey(moduleRef, pinRef) {
    return `${encodeURIComponent(moduleRef)}/${encodeURIComponent(pinRef)}`;
}
/**
 * Parses a compound pin/pad key produced by {@link pinPropertyKey} back into its parts.
 *
 * @param key - A key of the form `"encodedModuleRef/encodedPinRef"`.
 * @returns The decoded `moduleRef` and `pinRef`, or `null` if the key has no separator.
 */
function parsePinPropertyKey(key) {
    const slashIdx = key.indexOf('/');
    if (slashIdx < 0) {
        return null;
    }
    try {
        return {
            moduleRef: decodeURIComponent(key.slice(0, slashIdx)),
            pinRef: decodeURIComponent(key.slice(slashIdx + 1)),
        };
    }
    catch {
        return null;
    }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getAugmentedNamespace(n) {
  if (Object.prototype.hasOwnProperty.call(n, '__esModule')) return n;
  var f = n.default;
	if (typeof f == "function") {
		var a = function a () {
			var isInstance = false;
      try {
        isInstance = this instanceof a;
      } catch {}
			if (isInstance) {
        return Reflect.construct(f, arguments, this.constructor);
			}
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var Utils$5 = {};

var ChainableIterator = {};

var hasRequiredChainableIterator;

function requireChainableIterator () {
	if (hasRequiredChainableIterator) return ChainableIterator;
	hasRequiredChainableIterator = 1;
	Object.defineProperty(ChainableIterator, "__esModule", { value: true });
	ChainableIterator.ChainableIterator = ChainableIterator.EmptyIteratorError = void 0;
	class EmptyIteratorError extends Error {
	    constructor(message) {
	        super(message ?? 'Expected iterator to yield at least one element');
	        this.name = 'EmptyIteratorError';
	    }
	}
	ChainableIterator.EmptyIteratorError = EmptyIteratorError;
	class ChainableIterator$1 {
	    constructor(source) {
	        this.source = source;
	    }
	    static from(iterable) {
	        return new ChainableIterator$1(iterable);
	    }
	    [Symbol.iterator]() {
	        return this.source[Symbol.iterator]();
	    }
	    pipeMap(fn) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            let i = 0;
	            for (const item of source) {
	                yield fn(item, i++);
	            }
	        })());
	    }
	    pipeFilter(fn) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            let i = 0;
	            for (const item of source) {
	                if (fn(item, i++)) {
	                    yield item;
	                }
	            }
	        })());
	    }
	    pipeFlatMap(fn) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            let i = 0;
	            for (const item of source) {
	                yield* fn(item, i++);
	            }
	        })());
	    }
	    pipeTake(n) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            if (n <= 0) {
	                return;
	            }
	            let count = 0;
	            const iter = source[Symbol.iterator]();
	            let result = iter.next();
	            while (!result.done) {
	                yield result.value;
	                count++;
	                if (count >= n) {
	                    return;
	                }
	                result = iter.next();
	            }
	        })());
	    }
	    pipeSkip(n) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            let skipped = 0;
	            for (const item of source) {
	                if (skipped < n) {
	                    skipped++;
	                    continue;
	                }
	                yield item;
	            }
	        })());
	    }
	    pipeEnumerate() {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            let i = 0;
	            for (const item of source) {
	                yield [i++, item];
	            }
	        })());
	    }
	    pipeUnique(keyFn) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            const seen = new Set();
	            for (const item of source) {
	                const key = keyFn ? keyFn(item) : item;
	                if (!seen.has(key)) {
	                    seen.add(key);
	                    yield item;
	                }
	            }
	        })());
	    }
	    pipeConcat(...others) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            yield* source;
	            for (const other of others) {
	                yield* other;
	            }
	        })());
	    }
	    pipeAssertAtLeastOne(message) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            let yielded = false;
	            for (const item of source) {
	                yielded = true;
	                yield item;
	            }
	            if (!yielded) {
	                throw new EmptyIteratorError(message);
	            }
	        })());
	    }
	    pipeZip(other) {
	        const source = this.source;
	        return new ChainableIterator$1((function* () {
	            const iterA = source[Symbol.iterator]();
	            const iterB = other[Symbol.iterator]();
	            while (true) {
	                const a = iterA.next();
	                const b = iterB.next();
	                if (a.done || b.done) {
	                    break;
	                }
	                yield [a.value, b.value];
	            }
	        })());
	    }
	    reduce(fn, initial) {
	        let acc = initial;
	        let i = 0;
	        for (const item of this.source) {
	            acc = fn(acc, item, i++);
	        }
	        return acc;
	    }
	    forEach(fn) {
	        let i = 0;
	        for (const item of this.source) {
	            fn(item, i++);
	        }
	    }
	    find(fn) {
	        let i = 0;
	        for (const item of this.source) {
	            if (fn(item, i++)) {
	                return item;
	            }
	        }
	        return undefined;
	    }
	    some(fn) {
	        let i = 0;
	        for (const item of this.source) {
	            if (fn(item, i++)) {
	                return true;
	            }
	        }
	        return false;
	    }
	    every(fn) {
	        let i = 0;
	        for (const item of this.source) {
	            if (!fn(item, i++)) {
	                return false;
	            }
	        }
	        return true;
	    }
	    includes(value) {
	        for (const item of this.source) {
	            if (item === value) {
	                return true;
	            }
	        }
	        return false;
	    }
	    atLeastOne() {
	        for (const _ of this.source) {
	            return true;
	        }
	        return false;
	    }
	    count() {
	        let n = 0;
	        for (const _ of this.source) {
	            n++;
	        }
	        return n;
	    }
	    first() {
	        for (const item of this.source) {
	            return item;
	        }
	        return undefined;
	    }
	    toArray() {
	        return Array.from(this.source);
	    }
	    toSet() {
	        return new Set(this.source);
	    }
	    toMap() {
	        return new Map(this.source);
	    }
	    join(separator = ',') {
	        let result = '';
	        let first = true;
	        for (const item of this.source) {
	            if (!first) {
	                result += separator;
	            }
	            result += String(item);
	            first = false;
	        }
	        return result;
	    }
	    groupBy(keyFn) {
	        const groups = new Map();
	        for (const item of this.source) {
	            const key = keyFn(item);
	            const group = groups.get(key);
	            if (group) {
	                group.push(item);
	            }
	            else {
	                groups.set(key, [item]);
	            }
	        }
	        return groups;
	    }
	    partition(fn) {
	        const pass = [];
	        const fail = [];
	        for (const item of this.source) {
	            if (fn(item)) {
	                pass.push(item);
	            }
	            else {
	                fail.push(item);
	            }
	        }
	        return [pass, fail];
	    }
	}
	ChainableIterator.ChainableIterator = ChainableIterator$1;
	
	return ChainableIterator;
}

var IterableMap = {};

var SafeMap = {};

var hasRequiredSafeMap;

function requireSafeMap () {
	if (hasRequiredSafeMap) return SafeMap;
	hasRequiredSafeMap = 1;
	Object.defineProperty(SafeMap, "__esModule", { value: true });
	SafeMap.SafeMapKeyNotFoundError = SafeMap.SafeMap = void 0;
	class SafeMap$1 extends Map {
	    assertGet(key) {
	        if (!this.has(key)) {
	            throw new SafeMapKeyNotFoundError(key);
	        }
	        return this.get(key);
	    }
	}
	SafeMap.SafeMap = SafeMap$1;
	class SafeMapKeyNotFoundError extends Error {
	    constructor(key) {
	        super(`Key ${key} not found in safe map`);
	        this.key = key;
	    }
	}
	SafeMap.SafeMapKeyNotFoundError = SafeMapKeyNotFoundError;
	
	return SafeMap;
}

var hasRequiredIterableMap;

function requireIterableMap () {
	if (hasRequiredIterableMap) return IterableMap;
	hasRequiredIterableMap = 1;
	Object.defineProperty(IterableMap, "__esModule", { value: true });
	IterableMap.IterableMap = void 0;
	const SafeMap_1 = requireSafeMap();
	const ChainableIterator_1 = requireChainableIterator();
	class IterableMap$1 extends SafeMap_1.SafeMap {
	    iterKeys() {
	        return new ChainableIterator_1.ChainableIterator(super.keys());
	    }
	    iterValues() {
	        return new ChainableIterator_1.ChainableIterator(super.values());
	    }
	    iterEntries() {
	        return new ChainableIterator_1.ChainableIterator(super.entries());
	    }
	    static from(entries) {
	        return new IterableMap$1(entries);
	    }
	}
	IterableMap.IterableMap = IterableMap$1;
	
	return IterableMap;
}

var IterableSet = {};

var hasRequiredIterableSet;

function requireIterableSet () {
	if (hasRequiredIterableSet) return IterableSet;
	hasRequiredIterableSet = 1;
	Object.defineProperty(IterableSet, "__esModule", { value: true });
	IterableSet.IterableSet = void 0;
	const ChainableIterator_1 = requireChainableIterator();
	class IterableSet$1 extends Set {
	    iterValues() {
	        return new ChainableIterator_1.ChainableIterator(super.values());
	    }
	    static from(values) {
	        return new IterableSet$1(values);
	    }
	}
	IterableSet.IterableSet = IterableSet$1;
	
	return IterableSet;
}

var StrictMap = {};

var StrictTypeGuard = {};

var hasRequiredStrictTypeGuard;

function requireStrictTypeGuard () {
	if (hasRequiredStrictTypeGuard) return StrictTypeGuard;
	hasRequiredStrictTypeGuard = 1;
	Object.defineProperty(StrictTypeGuard, "__esModule", { value: true });
	StrictTypeGuard.StrictTypeGuard = StrictTypeGuard.StrictTypeMismatchError = void 0;
	class StrictTypeMismatchError extends Error {
	    constructor(expected, actual) {
	        super(`Strict type mismatch: expected exact type ${expected.name} but received ${actual.name}. ` +
	            `Subclass instances are not allowed — the object's constructor must be identical.`);
	        this.name = 'StrictTypeMismatchError';
	    }
	}
	StrictTypeGuard.StrictTypeMismatchError = StrictTypeMismatchError;
	class StrictTypeGuard$1 {
	    constructor(enforcedType) {
	        this.enforcedType = enforcedType;
	    }
	    get type() {
	        return this.enforcedType;
	    }
	    assertType(value) {
	        const actualCtor = value.constructor;
	        if (this.enforcedType === undefined) {
	            this.enforcedType = actualCtor;
	            return;
	        }
	        if (actualCtor !== this.enforcedType) {
	            throw new StrictTypeMismatchError(this.enforcedType, actualCtor);
	        }
	    }
	}
	StrictTypeGuard.StrictTypeGuard = StrictTypeGuard$1;
	
	return StrictTypeGuard;
}

var hasRequiredStrictMap;

function requireStrictMap () {
	if (hasRequiredStrictMap) return StrictMap;
	hasRequiredStrictMap = 1;
	Object.defineProperty(StrictMap, "__esModule", { value: true });
	StrictMap.StrictMap = void 0;
	const IterableMap_1 = requireIterableMap();
	const StrictTypeGuard_1 = requireStrictTypeGuard();
	class StrictMap$1 extends IterableMap_1.IterableMap {
	    constructor(enforcedKeyType, entries) {
	        super();
	        this.guard = new StrictTypeGuard_1.StrictTypeGuard(enforcedKeyType);
	        if (entries) {
	            for (const [key, value] of entries) {
	                this.set(key, value);
	            }
	        }
	    }
	    get enforcedKeyType() {
	        return this.guard.type;
	    }
	    set(key, value) {
	        this.guard.assertType(key);
	        return super.set(key, value);
	    }
	    get(key) {
	        this.guard.assertType(key);
	        return super.get(key);
	    }
	    has(key) {
	        this.guard.assertType(key);
	        return super.has(key);
	    }
	    delete(key) {
	        this.guard.assertType(key);
	        return super.delete(key);
	    }
	    assertGet(key) {
	        this.guard.assertType(key);
	        return super.assertGet(key);
	    }
	}
	StrictMap.StrictMap = StrictMap$1;
	
	return StrictMap;
}

var StrictSet = {};

var hasRequiredStrictSet;

function requireStrictSet () {
	if (hasRequiredStrictSet) return StrictSet;
	hasRequiredStrictSet = 1;
	Object.defineProperty(StrictSet, "__esModule", { value: true });
	StrictSet.StrictSet = void 0;
	const IterableSet_1 = requireIterableSet();
	const StrictTypeGuard_1 = requireStrictTypeGuard();
	class StrictSet$1 extends IterableSet_1.IterableSet {
	    constructor(enforcedType, values) {
	        super();
	        this.guard = new StrictTypeGuard_1.StrictTypeGuard(enforcedType);
	        if (values) {
	            for (const value of values) {
	                this.add(value);
	            }
	        }
	    }
	    get enforcedType() {
	        return this.guard.type;
	    }
	    add(value) {
	        this.guard.assertType(value);
	        return super.add(value);
	    }
	    has(value) {
	        this.guard.assertType(value);
	        return super.has(value);
	    }
	    delete(value) {
	        this.guard.assertType(value);
	        return super.delete(value);
	    }
	}
	StrictSet.StrictSet = StrictSet$1;
	
	return StrictSet;
}

var Transform$1 = {};

var Angles = {};

var hasRequiredAngles;

function requireAngles () {
	if (hasRequiredAngles) return Angles;
	hasRequiredAngles = 1;
	Object.defineProperty(Angles, "__esModule", { value: true });
	Angles.degreesToRadians = degreesToRadians;
	Angles.radiansToDegrees = radiansToDegrees;
	function degreesToRadians(degrees) {
	    return (degrees * Math.PI) / 180;
	}
	function radiansToDegrees(radians) {
	    return (radians * 180) / Math.PI;
	}
	
	return Angles;
}

var hasRequiredTransform;

function requireTransform () {
	if (hasRequiredTransform) return Transform$1;
	hasRequiredTransform = 1;
	Object.defineProperty(Transform$1, "__esModule", { value: true });
	Transform$1.toDrawing = toDrawing;
	Transform$1.range = range;
	Transform$1.clampRotation = clampRotation;
	Transform$1.roundToMultiple = roundToMultiple;
	Transform$1.floorToMultiple = floorToMultiple;
	Transform$1.ceilToMultiple = ceilToMultiple;
	Transform$1.roundToMaxOutputPositionPrecision = roundToMaxOutputPositionPrecision;
	Transform$1.roundToMaxOutputAnglePrecision = roundToMaxOutputAnglePrecision;
	Transform$1.roundEpsilonToMultiple = roundEpsilonToMultiple;
	Transform$1.getExactPrecisionNumber = getExactPrecisionNumber;
	Transform$1.getArcFromVector = getArcFromVector;
	Transform$1.transformDrawingByParent = transformDrawingByParent;
	Transform$1.areArraysEqualSets = areArraysEqualSets;
	Transform$1.rotateDrawing = rotateDrawing;
	Transform$1.rotatePoint = rotatePoint;
	Transform$1.roundToZero = roundToZero;
	Transform$1.translateDrawing = translateDrawing;
	Transform$1.translatePoint = translatePoint;
	Transform$1.subtractPoints = subtractPoints;
	Transform$1.scalePoint = scalePoint;
	Transform$1.getArrayDepth = getArrayDepth;
	Transform$1.getXyWithMirror = getXyWithMirror;
	Transform$1.getLineOffset = getLineOffset;
	Transform$1.rejustifyTextUpwards = rejustifyTextUpwards;
	Transform$1.normalizeTransformForFootprint = normalizeTransformForFootprint;
	Transform$1.getFootprintPinOne = getFootprintPinOne;
	Transform$1.calculateFootprintCenter = calculateFootprintCenter;
	Transform$1.transformDrawing = transformDrawing;
	Transform$1.applyTransformsToDrawing = applyTransformsToDrawing;
	const Angles_1 = requireAngles();
	function toDrawing(item) {
	    if (item.type === 'zone' && item.polygons.length === 1) {
	        const polyBase = item.polygons[0];
	        const asPoly = {
	            type: 'polygon',
	            points: polyBase.points,
	            width: polyBase.lineWidth || 0,
	            filled: polyBase.filled,
	        };
	        return asPoly;
	    }
	    if (item.type === 'track' || item.type === 'via' || item.type === 'zone') {
	        return item;
	    }
	    return undefined;
	}
	function range(startAt, endAt) {
	    return Array.from(new Array(endAt)).filter((l) => l >= startAt);
	}
	function clampRotation(degrees) {
	    let result = degrees;
	    while (result < 0) {
	        result += 360;
	    }
	    while (result >= 360) {
	        result -= 360;
	    }
	    if (result === -0) {
	        result = 0;
	    }
	    return result;
	}
	function roundToMultiple(num, multiple) {
	    return Math.round(num / multiple) * multiple;
	}
	function floorToMultiple(num, multiple) {
	    return Math.floor(num / multiple) * multiple;
	}
	function ceilToMultiple(num, multiple) {
	    return Math.ceil(num / multiple) * multiple;
	}
	function roundToMaxOutputPositionPrecision(num) {
	    return getExactPrecisionNumber(roundEpsilonToMultiple(roundToMultiple(num, 0.001), 0.001, 1), 3);
	}
	function roundToMaxOutputAnglePrecision(num) {
	    return getExactPrecisionNumber(roundEpsilonToMultiple(roundToMultiple(num, 0.01), 0.01, 1), 2);
	}
	function roundEpsilonToMultiple(num, multiple, numEpsilons = 2) {
	    const nearestMultiple = Math.round(num / multiple) * multiple;
	    if (Math.abs(num - nearestMultiple) < numEpsilons * Number.EPSILON) {
	        return nearestMultiple;
	    }
	    return num;
	}
	function getExactPrecisionNumber(number, precision) {
	    return parseFloat(number.toFixed(precision));
	}
	function getArcFromVector(start, end, degrees) {
	    const angle = degrees * (Math.PI / 180);
	    const xi = start[0];
	    const yi = start[1];
	    const xf = end[0];
	    const yf = end[1];
	    const radius = (Math.pow(xi - xf, 2) + Math.pow(yi - yf, 2)) / (2 * (xi - xf) * Math.cos(angle) + 2 * (yi - yf) * Math.sin(angle));
	    const xc = xi - radius * Math.cos(angle);
	    const yc = yi - radius * Math.sin(angle);
	    const startAngle = Math.atan2((Math.pow(xi - xf, 2) - Math.pow(yi - yf, 2)) * Math.sin(angle) - 2 * (xi - xf) * (yi - yf) * Math.cos(angle), (Math.pow(yi - yf, 2) - Math.pow(xi - xf, 2)) * Math.cos(angle) - 2 * (xi - xf) * (yi - yf) * Math.sin(angle)) - angle;
	    const endAngle = startAngle + angle;
	    return {
	        center: [xc, yc],
	        radius,
	        bearings: [startAngle, endAngle],
	    };
	}
	function transformDrawingByParent(drawing, parent) {
	    let shape = drawing;
	    if (parent.transform.angle) {
	        shape = rotateDrawing(shape, -parent.transform.angle);
	    }
	    if (parent.center) {
	        shape = translateDrawing(shape, parent.center);
	    }
	    else if (parent.pos) {
	        shape = translateDrawing(shape, parent.pos);
	    }
	    return shape;
	}
	function areArraysEqualSets(a1, a2) {
	    const superSet = {};
	    for (const i of a1) {
	        const e = i + typeof i;
	        superSet[e] = 1;
	    }
	    for (const i of a2) {
	        const e = i + typeof i;
	        if (!superSet[e]) {
	            return false;
	        }
	        superSet[e] = 2;
	    }
	    for (const e in superSet) {
	        if (superSet[e] === 1) {
	            return false;
	        }
	    }
	    return true;
	}
	function rotateDrawing(_input, degrees) {
	    const input = _input;
	    switch (input.type) {
	        case 'circle':
	            return {
	                ...input,
	                center: rotatePoint(input.center, degrees),
	            };
	        case 'polygon':
	            return {
	                ...input,
	                pos: input.pos && rotatePoint(input.pos, degrees),
	                points: input.points?.map((p) => rotatePoint(p, degrees)),
	            };
	        case 'rect':
	            return {
	                ...input,
	                start: rotatePoint(input.start, degrees),
	                end: rotatePoint(input.end, degrees),
	            };
	        case 'segment':
	            return {
	                ...input,
	                start: rotatePoint(input.start, degrees),
	                end: rotatePoint(input.end, degrees),
	            };
	        case 'track':
	            return {
	                ...input,
	                start: rotatePoint(input.start, degrees),
	                end: rotatePoint(input.end, degrees),
	            };
	        case 'via':
	            return {
	                ...input,
	                center: rotatePoint(input.center, degrees),
	            };
	        case 'zone':
	            return {
	                ...input,
	                polygons: input.polygons.map((poly) => ({
	                    ...poly,
	                    points: poly.points.map((p) => rotatePoint(p, degrees)),
	                })),
	            };
	        case 'pin':
	            return {
	                ...input,
	                start: rotatePoint(input.start, degrees),
	                end: rotatePoint(input.end, degrees),
	                drawings: input.drawings?.map((d) => ({
	                    ...d,
	                    drawing: rotateDrawing(d.drawing, degrees),
	                })),
	            };
	        case 'pad':
	            return {
	                ...input,
	                pos: rotatePoint(input.pos, degrees),
	                angle: input.angle + degrees,
	            };
	        case 'text':
	            return {
	                ...input,
	                pos: rotatePoint(input.pos, degrees),
	            };
	        case 'arc': {
	            let startAngle = input.startAngle + degrees;
	            let endAngle = input.endAngle + degrees;
	            startAngle = clampRotation(startAngle);
	            endAngle = clampRotation(endAngle);
	            if (startAngle > endAngle) {
	                endAngle += 360;
	            }
	            return {
	                ...input,
	                center: rotatePoint(input.center, degrees),
	                startAngle,
	                endAngle,
	            };
	        }
	        case 'curve':
	            return {
	                ...input,
	                start: rotatePoint(input.start, degrees),
	                cpa: rotatePoint(input.cpa, degrees),
	                cpb: rotatePoint(input.cpb, degrees),
	                end: rotatePoint(input.end, degrees),
	            };
	        case 'image':
	        case 'rich-text':
	            return {
	                ...input,
	                pos: rotatePoint(input.pos, degrees),
	            };
	        default:
	            throw new Error(`RotateDrawing was called for input '${input.type}', but it is not implemented.`);
	    }
	}
	function rotatePoint(point, degrees = 0, pivot) {
	    if (degrees === 0) {
	        return point;
	    }
	    const pivotX = pivot ? pivot[0] : 0;
	    const pivotY = pivot ? pivot[1] : 0;
	    const angle = degrees * (Math.PI / 180);
	    const rotatedX = Math.cos(angle) * (point[0] - pivotX) - Math.sin(angle) * (point[1] - pivotY) + pivotX;
	    const rotatedY = Math.sin(angle) * (point[0] - pivotX) + Math.cos(angle) * (point[1] - pivotY) + pivotY;
	    return [rotatedX, rotatedY];
	}
	function roundToZero(x, epsilonFactor = 1) {
	    if (Math.abs(x) <= Number.EPSILON * epsilonFactor) {
	        return 0;
	    }
	    return x;
	}
	function translateDrawing(_input, offset, mirror) {
	    const input = _input;
	    switch (input.type) {
	        case 'circle':
	            return {
	                ...input,
	                center: translatePoint(input.center, offset, mirror),
	            };
	        case 'polygon':
	            return {
	                ...input,
	                pos: input.pos && translatePoint(input.pos, offset, mirror),
	                points: input.points?.map((p) => translatePoint(p, offset, mirror)),
	            };
	        case 'rect':
	            return {
	                ...input,
	                start: translatePoint(input.start, offset, mirror),
	                end: translatePoint(input.end, offset, mirror),
	            };
	        case 'segment':
	            return {
	                ...input,
	                start: translatePoint(input.start, offset, mirror),
	                end: translatePoint(input.end, offset, mirror),
	            };
	        case 'track':
	            return {
	                ...input,
	                start: translatePoint(input.start, offset, mirror),
	                end: translatePoint(input.end, offset, mirror),
	            };
	        case 'via':
	            return {
	                ...input,
	                center: translatePoint(input.center, offset, mirror),
	            };
	        case 'zone':
	            return {
	                ...input,
	                polygons: input.polygons.map((poly) => ({
	                    ...poly,
	                    points: poly.points.map((p) => translatePoint(p, offset, mirror)),
	                })),
	            };
	        case 'pin':
	            return {
	                ...input,
	                start: translatePoint(input.start, offset, mirror),
	                end: translatePoint(input.end, offset, mirror),
	                drawings: input.drawings ?
	                    input.drawings.map((d) => ({
	                        ...d,
	                        drawing: translateDrawing(d.drawing, offset, mirror),
	                    }))
	                    : undefined,
	            };
	        case 'text':
	            return {
	                ...input,
	                pos: translatePoint(input.pos, offset, mirror),
	            };
	        case 'arc': {
	            let startAngle = input.startAngle;
	            let endAngle = input.endAngle;
	            if (mirror) {
	                if (mirror[1]) {
	                    startAngle = 180 - startAngle;
	                    endAngle = 180 - endAngle;
	                }
	                if (mirror[0]) {
	                    startAngle = 360 - startAngle;
	                    endAngle = 360 - endAngle;
	                }
	                const numMirrors = (mirror[0] ? 1 : 0) + (mirror[1] ? 1 : 0);
	                if (numMirrors % 2 === 1) {
	                    [startAngle, endAngle] = [endAngle, startAngle];
	                }
	                startAngle = clampRotation(startAngle);
	                endAngle = clampRotation(endAngle);
	                if (startAngle > endAngle) {
	                    endAngle += 360;
	                }
	            }
	            return {
	                ...input,
	                center: translatePoint(input.center, offset, mirror),
	                startAngle,
	                endAngle,
	            };
	        }
	        case 'pad':
	            return {
	                ...input,
	                pos: translatePoint(input.pos, offset, mirror),
	            };
	        case 'curve':
	            return {
	                ...input,
	                start: translatePoint(input.start, offset, mirror),
	                cpa: translatePoint(input.cpa, offset, mirror),
	                cpb: translatePoint(input.cpb, offset, mirror),
	                end: translatePoint(input.end, offset, mirror),
	            };
	        case 'image':
	        case 'rich-text':
	            return {
	                ...input,
	                pos: translatePoint(input.pos, offset, mirror),
	            };
	        default:
	            throw Error(`TranslateDrawing was called with '${input.type}', but it is not implemented`);
	    }
	}
	function translatePoint(point, offset, mirror) {
	    if (mirror) {
	        point = getXyWithMirror(point, mirror);
	    }
	    return [point[0] + offset[0], point[1] + offset[1]];
	}
	function subtractPoints(point, offset) {
	    return [point[0] - offset[0], point[1] - offset[1]];
	}
	function scalePoint(point, scaleFactor) {
	    if (typeof scaleFactor === 'number') {
	        return [point[0] * scaleFactor, point[1] * scaleFactor];
	    }
	    return [point[0] * scaleFactor[0], point[1] * scaleFactor[1]];
	}
	function getArrayDepth(value) {
	    return Array.isArray(value) ? 1 + Math.max(...value.map(getArrayDepth)) : 0;
	}
	function getXyWithMirror(point, mirror) {
	    return [mirror?.[1] ? -point[0] : point[0], mirror?.[0] ? -point[1] : point[1]];
	}
	function getLineOffset(length, rotation, start) {
	    let end = [length, 0];
	    end = rotatePoint(end, rotation);
	    end = translatePoint(end, start);
	    end = [roundToZero(end[0], 2), roundToZero(end[1], 2)];
	    return end;
	}
	function rejustifyTextUpwards(justify, rot) {
	    rot.rot = roundEpsilonToMultiple(clampRotation(rot.rot), 5);
	    if (rot.rot > 90 && rot.rot <= 270) {
	        return {
	            justify: [-justify[0], -justify[1]],
	            rot: {
	                rot: rot.rot,
	                mirrored: [!rot.mirrored[0], !rot.mirrored[1]],
	            },
	        };
	    }
	    return { justify, rot };
	}
	function normalizeTransformForFootprint(layerSet, footprint, compareToFp) {
	    const transform = {
	        angle: 0,
	        mirrored: [false, false],
	    };
	    if (!footprint || !footprint.pads.length) {
	        return transform;
	    }
	    const center = calculateFootprintCenter(footprint);
	    if (Math.abs(center[0]) > Number.EPSILON || Math.abs(center[1]) > Number.EPSILON) {
	        transform.pos = [-center[0], -center[1]];
	    }
	    const pin1 = translateDrawing(getFootprintPinOne(footprint), transform.pos ?? [0, 0]);
	    transform.angle = clampRotation(-(0, Angles_1.radiansToDegrees)(Math.atan2(pin1.pos[1], pin1.pos[0])));
	    let surfaceLayerChecked = false;
	    if (pin1.padType === 'smd') {
	        surfaceLayerChecked = true;
	        const surfaceLayers = pin1.layers?.map((l) => layerSet.find((ls) => ls.face && ls.id === l)) ?? [];
	        if (surfaceLayers.length === 1 && surfaceLayers[0]?.face === 'B') {
	            transform.mirrored = [true, true];
	        }
	    }
	    if (!surfaceLayerChecked) {
	        if (pin1.padType === 'smd' && compareToFp) {
	            const compareToPin1 = getFootprintPinOne(compareToFp);
	            const myLayer = pin1.layers?.[0];
	            const compareLayer = compareToPin1.layers?.[0];
	            if (myLayer !== compareLayer) {
	                transform.mirrored = [true, true];
	            }
	        }
	        else if (pin1.padType === 'th' && compareToFp) {
	            const mySmdPads = footprint.pads.filter((p) => p.padType === 'smd');
	            const myLayer = mySmdPads.reduce((sum, p) => sum + p.layers[0], 0);
	            const compareSmdPads = compareToFp.pads.filter((p) => p.padType === 'smd');
	            const compareLayer = compareSmdPads.reduce((sum, p) => sum + p.layers[0], 0);
	            if (mySmdPads.length === compareSmdPads.length && myLayer !== compareLayer) {
	                transform.mirrored = [true, true];
	            }
	        }
	    }
	    return transform;
	}
	function getFootprintPinOne(footprint) {
	    const declaredPinOne = footprint.pads.find((p) => !!p.pin1);
	    if (declaredPinOne) {
	        return declaredPinOne;
	    }
	    const pins = [...footprint.pads].sort((a, b) => a.ref.localeCompare(b.ref));
	    return pins[0];
	}
	function calculateFootprintCenter(footprint) {
	    const center = [0, 0];
	    let numPads = 0;
	    for (const pad of footprint.pads) {
	        if (pad.pos) {
	            center[0] += pad.pos[0];
	            center[1] += pad.pos[1];
	            numPads++;
	        }
	    }
	    if (numPads) {
	        center[0] /= numPads;
	        center[1] /= numPads;
	    }
	    return center;
	}
	function transformDrawing(drawing, transform) {
	    let result = drawing;
	    result = translateDrawing(result, transform.pos ?? [0, 0], transform.mirrored);
	    if (transform.angle) {
	        result = rotateDrawing(result, transform.angle);
	    }
	    return result;
	}
	function applyTransformsToDrawing(drawing, transforms) {
	    return transforms.reduce((d, t) => transformDrawing(d, t), drawing);
	}
	
	return Transform$1;
}

var asciiConversion = {};

var hasRequiredAsciiConversion;

function requireAsciiConversion () {
	if (hasRequiredAsciiConversion) return asciiConversion;
	hasRequiredAsciiConversion = 1;
	Object.defineProperty(asciiConversion, "__esModule", { value: true });
	asciiConversion.convertStringExpressionToAscii = convertStringExpressionToAscii;
	function convertStringExpressionToAscii(input) {
	    if (!input) {
	        return input;
	    }
	    let result = input;
	    result = result.replace(/×/g, '*');
	    result = result.replace(/·/g, '*');
	    result = result.replace(/⋅/g, '*');
	    result = result.replace(/÷/g, '/');
	    result = result.replace(/±/g, '+/-');
	    result = result.replace(/−/g, '-');
	    result = result.replace(/[–—]/g, '-');
	    result = result.replace(/≤/g, '<=');
	    result = result.replace(/≥/g, '>=');
	    result = result.replace(/≠/g, '!=');
	    result = result.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁻]+/g, (match) => {
	        const converted = match
	            .replace(/⁰/g, '0')
	            .replace(/¹/g, '1')
	            .replace(/²/g, '2')
	            .replace(/³/g, '3')
	            .replace(/⁴/g, '4')
	            .replace(/⁵/g, '5')
	            .replace(/⁶/g, '6')
	            .replace(/⁷/g, '7')
	            .replace(/⁸/g, '8')
	            .replace(/⁹/g, '9')
	            .replace(/⁻/g, '-');
	        return '^' + converted;
	    });
	    result = result.replace(/[₀₁₂₃₄₅₆₇₈₉]+/g, (match) => {
	        const converted = match
	            .replace(/₀/g, '0')
	            .replace(/₁/g, '1')
	            .replace(/₂/g, '2')
	            .replace(/₃/g, '3')
	            .replace(/₄/g, '4')
	            .replace(/₅/g, '5')
	            .replace(/₆/g, '6')
	            .replace(/₇/g, '7')
	            .replace(/₈/g, '8')
	            .replace(/₉/g, '9');
	        return '_' + converted;
	    });
	    return result;
	}
	
	return asciiConversion;
}

var ModelBoundingBoxUtils = {};

var hasRequiredModelBoundingBoxUtils;

function requireModelBoundingBoxUtils () {
	if (hasRequiredModelBoundingBoxUtils) return ModelBoundingBoxUtils;
	hasRequiredModelBoundingBoxUtils = 1;
	Object.defineProperty(ModelBoundingBoxUtils, "__esModule", { value: true });
	ModelBoundingBoxUtils.padBboxToAspectRatio = padBboxToAspectRatio;
	function padBboxToAspectRatio(bbox, maxAspectRatio) {
	    const w = bbox.maxx - bbox.minx;
	    const h = bbox.maxy - bbox.miny;
	    if (w <= 0 || h <= 0) {
	        return bbox;
	    }
	    if (w / h > maxAspectRatio) {
	        const targetH = w / maxAspectRatio;
	        const pad = (targetH - h) / 2;
	        return { ...bbox, miny: bbox.miny - pad, maxy: bbox.maxy + pad };
	    }
	    if (h / w > maxAspectRatio) {
	        const targetW = h / maxAspectRatio;
	        const pad = (targetW - w) / 2;
	        return { ...bbox, minx: bbox.minx - pad, maxx: bbox.maxx + pad };
	    }
	    return bbox;
	}
	
	return ModelBoundingBoxUtils;
}

var ClassConstructor = {};

var hasRequiredClassConstructor;

function requireClassConstructor () {
	if (hasRequiredClassConstructor) return ClassConstructor;
	hasRequiredClassConstructor = 1;
	Object.defineProperty(ClassConstructor, "__esModule", { value: true });
	
	return ClassConstructor;
}

var DateUtils = {};

var hasRequiredDateUtils;

function requireDateUtils () {
	if (hasRequiredDateUtils) return DateUtils;
	hasRequiredDateUtils = 1;
	Object.defineProperty(DateUtils, "__esModule", { value: true });
	DateUtils.floorUtcHour = floorUtcHour;
	DateUtils.floorUtcDay = floorUtcDay;
	DateUtils.addUtcMonths = addUtcMonths;
	const DAY_MS = 24 * 60 * 60 * 1000;
	function floorUtcHour(date) {
	    const floored = new Date(date);
	    floored.setUTCMinutes(0, 0, 0);
	    return floored;
	}
	function floorUtcDay(date) {
	    return new Date(Math.floor(date.getTime() / DAY_MS) * DAY_MS);
	}
	function addUtcMonths(date, months) {
	    const result = new Date(date);
	    const day = result.getUTCDate();
	    result.setUTCDate(1);
	    result.setUTCMonth(result.getUTCMonth() + months);
	    const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
	    result.setUTCDate(Math.min(day, lastDay));
	    return result;
	}
	
	return DateUtils;
}

var _Error = {};

var hasRequired_Error;

function require_Error () {
	if (hasRequired_Error) return _Error;
	hasRequired_Error = 1;
	Object.defineProperty(_Error, "__esModule", { value: true });
	_Error.NestedError = void 0;
	_Error.errorToJson = errorToJson;
	_Error.errorToJsonString = errorToJsonString;
	_Error.catchToJsonString = catchToJsonString;
	_Error.catchToError = catchToError;
	_Error.catchToErrorJson = catchToErrorJson;
	_Error.rootCauseMessage = rootCauseMessage;
	_Error.isErrorLike = isErrorLike;
	const MAX_SERIALIZED_ERROR_DEPTH = 8;
	const MAX_SERIALIZED_NESTED_ERRORS = 40;
	function errorToJson(error) {
	    if (!error) {
	        return null;
	    }
	    return serializeError(error, 0, { ancestors: new Set(), remaining: MAX_SERIALIZED_NESTED_ERRORS });
	}
	function serializeError(error, depth, state) {
	    if ('toJSON' in error && typeof error.toJSON === 'function') {
	        const serialized = error.toJSON();
	        if (typeof serialized === 'object' && serialized !== null) {
	            return serialized;
	        }
	    }
	    const result = {
	        message: error.message,
	        stack: error.stack,
	        name: error.name,
	    };
	    if (depth >= MAX_SERIALIZED_ERROR_DEPTH) {
	        return result;
	    }
	    state.ancestors.add(error);
	    const serializeInner = (inner) => {
	        if (state.remaining <= 0 || state.ancestors.has(inner)) {
	            return undefined;
	        }
	        state.remaining--;
	        return serializeError(catchToError(inner), depth + 1, state);
	    };
	    const cause = 'cause' in error ? error.cause : undefined;
	    if (cause !== undefined && cause !== null) {
	        const serializedCause = serializeInner(cause);
	        if (serializedCause) {
	            result.cause = serializedCause;
	        }
	    }
	    if (error instanceof AggregateError && Array.isArray(error.errors) && error.errors.length > 0) {
	        const inner = error.errors;
	        const serializedErrors = inner.map(serializeInner).filter((serialized) => serialized !== undefined);
	        if (serializedErrors.length > 0) {
	            result.errors = serializedErrors;
	        }
	        if (serializedErrors.length < inner.length) {
	            result.errorsTotalCount = inner.length;
	        }
	    }
	    state.ancestors.delete(error);
	    return result;
	}
	function errorToJsonString(error) {
	    if (!error) {
	        return null;
	    }
	    return JSON.stringify(errorToJson(error));
	}
	function catchToJsonString(error) {
	    if (error instanceof Error) {
	        return errorToJsonString(error);
	    }
	    return JSON.stringify({
	        message: String(error),
	    });
	}
	function catchToError(error) {
	    if (error instanceof Error) {
	        return error;
	    }
	    if (isErrorLike(error)) {
	        const err = new Error(error.message);
	        if (error.name) {
	            err.name = error.name;
	        }
	        if (error.stack) {
	            err.stack = error.stack;
	        }
	        return err;
	    }
	    if (typeof error === 'object') {
	        try {
	            return new Error(JSON.stringify(error));
	        }
	        catch {
	            return new Error(String(error));
	        }
	    }
	    return new Error(String(error));
	}
	function catchToErrorJson(error) {
	    return errorToJson(catchToError(error));
	}
	const MAX_CAUSE_DEPTH = 16;
	function rootCauseMessage(error) {
	    let deepest = catchToError(error).message;
	    let current = error;
	    const seen = new Set();
	    for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
	        if (typeof current !== 'object' || current === null || seen.has(current)) {
	            break;
	        }
	        seen.add(current);
	        const cause = current.cause;
	        if (cause === undefined || cause === null) {
	            break;
	        }
	        const message = isErrorLike(cause) ? cause.message
	            : typeof cause === 'string' ? cause
	                : '';
	        if (message.length > 0) {
	            deepest = message;
	        }
	        current = cause;
	    }
	    return deepest;
	}
	function isErrorLike(error) {
	    return (typeof error === 'object' &&
	        error !== null &&
	        typeof error.message === 'string' &&
	        (typeof error.stack === 'string' || typeof error.stack === 'undefined') &&
	        (typeof error.name === 'string' || typeof error.name === 'undefined'));
	}
	class NestedError extends Error {
	    constructor(m, innerError) {
	        super(m);
	        this.innerError = innerError;
	        Object.setPrototypeOf(this, NestedError.prototype);
	    }
	    toJSON() {
	        return {
	            message: this.message,
	            stack: this.stack,
	            name: this.name,
	            innerError: errorToJson(this.innerError),
	        };
	    }
	}
	_Error.NestedError = NestedError;
	
	return _Error;
}

var hash = {};

var hasRequiredHash;

function requireHash () {
	if (hasRequiredHash) return hash;
	hasRequiredHash = 1;
	Object.defineProperty(hash, "__esModule", { value: true });
	hash.fnv1aHash = fnv1aHash;
	function fnv1aHash(str) {
	    let hash = 0x811c9dc5;
	    for (let i = 0; i < str.length; i++) {
	        hash ^= str.charCodeAt(i);
	        hash = (hash * 0x01000193) >>> 0;
	    }
	    return hash.toString(16).padStart(8, '0');
	}
	
	return hash;
}

var namedCallStack = {};

var hasRequiredNamedCallStack;

function requireNamedCallStack () {
	if (hasRequiredNamedCallStack) return namedCallStack;
	hasRequiredNamedCallStack = 1;
	Object.defineProperty(namedCallStack, "__esModule", { value: true });
	namedCallStack.renameCallStack = renameCallStack;
	namedCallStack.namedCallStack = namedCallStack$1;
	function renameCallStack(error, name, message, removeStackLines = 0) {
	    if (!error.stack) {
	        return undefined;
	    }
	    return error.stack
	        .toString()
	        .replace(/^Error/, message ? `${name}: ${message}` : `${name}:`)
	        .replace(new RegExp(`(\\n\\s+at .*){${removeStackLines + 1}}`), '');
	}
	function namedCallStack$1(name, message, removeStackLines = 0) {
	    return renameCallStack(new Error(), name, message, removeStackLines);
	}
	
	return namedCallStack;
}

var naturalStringSort = {};

var hasRequiredNaturalStringSort;

function requireNaturalStringSort () {
	if (hasRequiredNaturalStringSort) return naturalStringSort;
	hasRequiredNaturalStringSort = 1;
	Object.defineProperty(naturalStringSort, "__esModule", { value: true });
	naturalStringSort.naturalStringSort = void 0;
	const reSplitAlphaNumeric = /([0-9]+)/gm;
	const naturalStringSort$1 = (aStr, bStr, { caseSensitive = true } = {}) => {
	    if (!caseSensitive) {
	        aStr = aStr.toLowerCase();
	        bStr = bStr.toLowerCase();
	    }
	    const a = aStr.split(reSplitAlphaNumeric).filter(Boolean);
	    const b = bStr.split(reSplitAlphaNumeric).filter(Boolean);
	    while (a.length && b.length) {
	        const aa = a.shift();
	        const bb = b.shift();
	        const an = parseInt(aa, 10);
	        const bn = parseInt(bb, 10);
	        const combo = [an, bn].sort();
	        if (isNaN(combo[0])) {
	            if (aa > bb) {
	                return 1;
	            }
	            if (bb > aa) {
	                return -1;
	            }
	            continue;
	        }
	        if (isNaN(combo[1])) {
	            return isNaN(an) ? -1 : 1;
	        }
	        if (an > bn) {
	            return 1;
	        }
	        if (bn > an) {
	            return -1;
	        }
	    }
	    return a.length - b.length;
	};
	naturalStringSort.naturalStringSort = naturalStringSort$1;
	
	return naturalStringSort;
}

var normalizeSiString = {};

var SIValueTool = {};

var RkmCodeValueTool = {};

var hasRequiredRkmCodeValueTool;

function requireRkmCodeValueTool () {
	if (hasRequiredRkmCodeValueTool) return RkmCodeValueTool;
	hasRequiredRkmCodeValueTool = 1;
	Object.defineProperty(RkmCodeValueTool, "__esModule", { value: true });
	RkmCodeValueTool.convertRkmValueToSiValueStr = convertRkmValueToSiValueStr;
	const magnitudeMap = {
	    P: 'p',
	    N: 'n',
	    U: 'u',
	    L: 'm',
	    E: '',
	    R: '',
	    F: '',
	    V: '',
	    v: '',
	    H: '',
	    K: 'k',
	};
	const unitsMap = {
	    V: 'V',
	    F: 'F',
	    L: 'F',
	    R: 'Ω',
	    E: 'Ω',
	};
	const standardRegex = /^([+-]?)\s*(\d+)\s*(\D{1})\s*(\d*)\s*([A-Za-z]*)$/;
	const alternateRegex = /^([+-]?)(\s*)(\D{1})\s*(\d+)\s*(\w*)$/;
	function convertRkmValueToSiValueStr(value) {
	    let wholePart;
	    let fractionalPart;
	    let units;
	    let match;
	    value = value.trim();
	    match = standardRegex.exec(value);
	    match = match ?? alternateRegex.exec(value);
	    if (!match) {
	        return undefined;
	    }
	    const sign = match[1];
	    wholePart = match[2].trim();
	    wholePart = wholePart ? wholePart : '0';
	    const code = match[3].trim();
	    if (!code) {
	        return undefined;
	    }
	    const magnitude = normalizeLetterCode(code);
	    fractionalPart = match[4];
	    fractionalPart = fractionalPart ? '.' + fractionalPart : '';
	    units = match[5];
	    units = units ? units : getUnitsFromCode(code);
	    return `${sign}${wholePart}${fractionalPart}${magnitude ?? ''}${units ?? ''}`;
	}
	function normalizeLetterCode(input) {
	    if (input in magnitudeMap) {
	        return magnitudeMap[input];
	    }
	    return input;
	}
	function getUnitsFromCode(code) {
	    code = code.toUpperCase();
	    if (code in unitsMap) {
	        return unitsMap[code];
	    }
	    return '';
	}
	
	return RkmCodeValueTool;
}

var hasRequiredSIValueTool;

function requireSIValueTool () {
	if (hasRequiredSIValueTool) return SIValueTool;
	hasRequiredSIValueTool = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.siPrefixes = void 0;
		exports.correctFloatingPointErrors = correctFloatingPointErrors;
		exports.formatNumericalSIValue = formatNumericalSIValue;
		exports.parseNumericalSIValue = parseNumericalSIValue;
		const RkmCodeValueTool_1 = requireRkmCodeValueTool();
		exports.siPrefixes = [
		    {
		        name: 'E',
		        factor: 1e18,
		        match: (unitsStr) => unitsStr === 'E' || unitsStr.toLowerCase() === 'exa',
		        transform: (value) => parseNumber(value) * 1e18,
		    },
		    {
		        name: 'P',
		        factor: 1e15,
		        match: (unitsStr) => unitsStr === 'P' || unitsStr.toLowerCase() === 'peta',
		        transform: (value) => parseNumber(value) * 1e15,
		    },
		    {
		        name: 'T',
		        factor: 1e12,
		        match: (unitsStr) => unitsStr.toUpperCase() === 'T' || unitsStr.toLowerCase() === 'tera',
		        transform: (value) => parseNumber(value) * 1e12,
		    },
		    {
		        name: 'G',
		        factor: 1e9,
		        match: (unitsStr) => unitsStr.toUpperCase() === 'G' || unitsStr.toLowerCase() === 'giga',
		        transform: (value) => parseNumber(value) * 1e9,
		    },
		    {
		        name: 'M',
		        factor: 1e6,
		        match: (unitsStr) => unitsStr === 'M' || unitsStr.toLowerCase() === 'mega',
		        transform: (value) => parseNumber(value) * 1e6,
		    },
		    {
		        name: 'k',
		        factor: 1e3,
		        match: (unitsStr) => ['k', 'kilo'].includes(unitsStr.toLowerCase()),
		        transform: (value) => parseNumber(value) * 1e3,
		    },
		    {
		        name: '',
		        factor: 1,
		        match: (unitsStr) => unitsStr === '',
		        transform: (value) => parseNumber(value),
		    },
		    {
		        name: 'd',
		        factor: 1e-1,
		        match: (unitsStr) => unitsStr.toLowerCase() === 'd' || unitsStr.toLowerCase() === 'deci',
		        transform: (value) => parseNumber(value) * 1e-1,
		    },
		    {
		        name: 'c',
		        factor: 1e-2,
		        match: (unitsStr) => unitsStr.toLowerCase() === 'c' || unitsStr.toLowerCase() === 'centi',
		        transform: (value) => parseNumber(value) * 1e-2,
		    },
		    {
		        name: 'm',
		        factor: 1e-3,
		        match: (unitsStr) => unitsStr === 'm' || unitsStr.toLowerCase() === 'milli',
		        transform: (value) => parseNumber(value) * 1e-3,
		    },
		    {
		        name: 'µ',
		        factor: 1e-6,
		        match: (unitsStr) => unitsStr.toLowerCase() === 'u' || unitsStr === 'µ' || unitsStr === 'μ' || unitsStr.toLowerCase() === 'micro',
		        transform: (value) => parseNumber(value) * 1e-6,
		    },
		    {
		        name: 'n',
		        factor: 1e-9,
		        match: (unitsStr) => unitsStr.toLowerCase() === 'n' || unitsStr.toLowerCase() === 'nano',
		        transform: (value) => parseNumber(value) * 1e-9,
		    },
		    {
		        name: 'p',
		        factor: 1e-12,
		        match: (unitsStr) => unitsStr === 'p' || unitsStr.toLowerCase() === 'pico',
		        transform: (value) => parseNumber(value) * 1e-12,
		    },
		    {
		        name: 'f',
		        factor: 1e-15,
		        match: (unitsStr) => unitsStr.toLowerCase() === 'f' || unitsStr.toLowerCase() === 'femto',
		        transform: (value) => parseNumber(value) * 1e-15,
		    },
		];
		function correctFloatingPointErrors(value) {
		    if (!Number.isFinite(value)) {
		        return value;
		    }
		    return parseFloat(value.toPrecision(15));
		}
		function formatNumericalSIValue(value, precision = 3) {
		    if (value === 0) {
		        return '0';
		    }
		    let sig = Math.abs(value);
		    let exponent = 0;
		    while (sig >= 1000 && exponent < 24) {
		        sig /= 1000;
		        exponent += 3;
		    }
		    while (sig < 1 && exponent > -24) {
		        sig *= 1000;
		        exponent -= 3;
		    }
		    const prefix = exports.siPrefixes.find((p) => p.factor === Math.pow(10, exponent));
		    const prefixName = prefix ? prefix.name : '';
		    const signPrefix = value < 0 ? '-' : '';
		    if (sig > 1000) {
		        return signPrefix + sig.toFixed(0) + prefixName;
		    }
		    return signPrefix + parseFloat(sig.toPrecision(precision)) + prefixName;
		}
		function parseNumericalSIValue(value, { short, long, alternateNames: alternates, usesSIPrefixes }) {
		    if (typeof value === 'undefined' || value === null) {
		        return undefined;
		    }
		    if (typeof value === 'number') {
		        return value;
		    }
		    value = value.trim();
		    if (value.startsWith('.')) {
		        value = '0' + value;
		    }
		    let firstException;
		    try {
		        return maybeParseSiValue(value, { short, long, alternateNames: alternates, usesSIPrefixes });
		    }
		    catch (ex) {
		        firstException = ex && ex instanceof Error ? ex : new Error(`Error parsing SI value: ${ex}`);
		    }
		    const valueStr = (0, RkmCodeValueTool_1.convertRkmValueToSiValueStr)(value);
		    if (!valueStr) {
		        throw firstException;
		    }
		    try {
		        return maybeParseSiValue(valueStr, { short, long, alternateNames: alternates, usesSIPrefixes });
		    }
		    catch {
		        throw firstException;
		    }
		}
		function maybeParseSiValue(value, { short, long, alternateNames: alternates, usesSIPrefixes }) {
		    if (typeof value === 'undefined' || value === null) {
		        return undefined;
		    }
		    if (typeof value === 'number') {
		        return value;
		    }
		    value = value.trim();
		    if (/^-?\d*(\.\d*)?(e[-+]?[0-9]+)?\s*$/.test(value) && /\d+/.test(value)) {
		        return parseNumber(value);
		    }
		    const searchUnits = [long, short, ...(alternates || [])];
		    const baseRegex = /^(\+?-?\s*\d*(\.\d*)?(e[-+]?\d+)?)\s*(\S*)$/i;
		    let [, numerical, , , units] = baseRegex.exec(value) || [];
		    units = units?.trim();
		    const unitsLower = units?.toLowerCase();
		    numerical = numerical?.replace(/\s+/g, '');
		    if (!units || units.length === 0) {
		        return parseNumber(numerical);
		    }
		    let siChars = units;
		    for (let searchUnit of searchUnits) {
		        if (!searchUnit) {
		            continue;
		        }
		        searchUnit = searchUnit.toLowerCase();
		        if (unitsLower && unitsLower.endsWith(searchUnit)) {
		            siChars = units.substring(0, unitsLower.lastIndexOf(searchUnit)).trim();
		            break;
		        }
		    }
		    if (usesSIPrefixes && siChars.length > 0) {
		        for (const si of exports.siPrefixes) {
		            if (si.match(siChars)) {
		                return correctFloatingPointErrors(si.transform(numerical));
		            }
		        }
		    }
		    if (typeof siChars === 'undefined' || siChars.length > 0) {
		        if (usesSIPrefixes) {
		            throw new Error(`Error parsing SI value: '${value}'. Could not match one of expected units: '${searchUnits.join("', '")}'`);
		        }
		        else {
		            throw new Error(`Error parsing value: '${value}'. Could not match one of expected units: '${searchUnits.join("', '")}'`);
		        }
		    }
		    return parseNumber(numerical);
		}
		function parseNumber(num) {
		    if (typeof num === 'number') {
		        return num;
		    }
		    else if (typeof num === 'string') {
		        num = num.replace(/\s+/g, '');
		        return Number(num);
		    }
		    else {
		        throw new Error(`Cannot parse number from ${typeof num}: ${num}`);
		    }
		}
		
	} (SIValueTool));
	return SIValueTool;
}

var CommonParameterUnits = {};

var hasRequiredCommonParameterUnits;

function requireCommonParameterUnits () {
	if (hasRequiredCommonParameterUnits) return CommonParameterUnits;
	hasRequiredCommonParameterUnits = 1;
	Object.defineProperty(CommonParameterUnits, "__esModule", { value: true });
	CommonParameterUnits.CommonParameterUnits = void 0;
	CommonParameterUnits.CommonParameterUnits = {
	    voltage: {
	        short: 'V',
	        long: 'Volts',
	        alternateNames: ['volts', 'volt'],
	        usesSIPrefixes: true,
	    },
	    current: {
	        short: 'A',
	        long: 'Amps',
	        alternateNames: ['amps', 'amp'],
	        usesSIPrefixes: true,
	    },
	    power: {
	        short: 'W',
	        long: 'Watts',
	        alternateNames: ['watts', 'watt'],
	        usesSIPrefixes: true,
	    },
	    resistance: {
	        short: 'Ω',
	        long: 'Ohms',
	        alternateNames: ['ohms', 'ohm', 'r', 'o'],
	        usesSIPrefixes: true,
	    },
	    capacitance: {
	        short: 'F',
	        long: 'Farads',
	        alternateNames: ['farads', 'farad'],
	        usesSIPrefixes: true,
	    },
	    inductance: {
	        short: 'H',
	        long: 'Henries',
	        alternateNames: ['henries', 'henry'],
	        usesSIPrefixes: true,
	    },
	    frequency: {
	        short: 'Hz',
	        long: 'Hertz',
	        alternateNames: ['hertz'],
	        usesSIPrefixes: true,
	    },
	    time: {
	        short: 's',
	        long: 'Seconds',
	        alternateNames: ['seconds', 'second'],
	        usesSIPrefixes: true,
	    },
	    temperature: {
	        short: 'C',
	        long: 'Celsius',
	        alternateNames: ['celsius', 'c'],
	    },
	    temperatureKelvin: {
	        short: 'K',
	        long: 'Kelvin',
	        alternateNames: ['kelvin', 'k'],
	    },
	    gain: {
	        short: 'dB',
	        long: 'Decibels',
	        alternateNames: ['decibels', 'db'],
	    },
	    percentage: {
	        short: '%',
	        long: 'Percentage',
	        alternateNames: ['percent', 'percentage'],
	    },
	    unitless: {
	        short: '',
	        long: '',
	        usesSIPrefixes: true,
	    },
	};
	
	return CommonParameterUnits;
}

var hasRequiredNormalizeSiString;

function requireNormalizeSiString () {
	if (hasRequiredNormalizeSiString) return normalizeSiString;
	hasRequiredNormalizeSiString = 1;
	Object.defineProperty(normalizeSiString, "__esModule", { value: true });
	normalizeSiString.normalizeSiString = normalizeSiString$1;
	const SIValueTool_1 = requireSIValueTool();
	const CommonParameterUnits_1 = requireCommonParameterUnits();
	function normalizeSiString$1(input) {
	    if (typeof input === 'number') {
	        input = input.toString();
	    }
	    const normalizedInput = input.replace(/[\t\u00A0\u2009\u2028\u2029\u202F\u205F\u3000]/g, ' ').trim();
	    if (normalizedInput.includes('\n')) {
	        return undefined;
	    }
	    const pattern = /([+-]?)\s*(\d+(?:,\d{3})*(?:\.\d*)?|\d*\.\d+)(?:[eE]([+-]?\d+))?\s*([a-zA-Zµ]*)\s*([a-zA-ZΩµ]*)/g;
	    const foundMatches = [];
	    let regexMatch;
	    while ((regexMatch = pattern.exec(normalizedInput)) !== null) {
	        const [fullMatch, sign = '', numberStr, exponent = '', prefix = '', unit = ''] = regexMatch;
	        if (numberStr === undefined || numberStr.trim() === '') {
	            continue;
	        }
	        let numValue = parseFloat(numberStr.replace(/,/g, ''));
	        if (exponent && exponent.trim() !== '') {
	            numValue *= Math.pow(10, parseInt(exponent, 10));
	        }
	        if (sign === '-') {
	            numValue = -numValue;
	        }
	        if (isNaN(numValue)) {
	            continue;
	        }
	        let multiplier = 1;
	        let isValidSiValue = true;
	        const combinedSuffix = prefix + unit;
	        if (combinedSuffix) {
	            let foundValidInterpretation = false;
	            if (prefix && unit) {
	                if (prefix in siPrefixes && siUnits.includes(unit)) {
	                    const prefixMultiplier = siPrefixes[prefix];
	                    if (prefixMultiplier !== undefined) {
	                        multiplier = prefixMultiplier;
	                        foundValidInterpretation = true;
	                    }
	                }
	            }
	            if (!foundValidInterpretation && prefix) {
	                if (prefix in siPrefixes) {
	                    const prefixMultiplier = siPrefixes[prefix];
	                    if (prefixMultiplier !== undefined) {
	                        multiplier = prefixMultiplier;
	                        foundValidInterpretation = true;
	                        if (unit && !siUnits.includes(unit)) {
	                            const prefixOnlyMatch = fullMatch.replace(new RegExp(`\\s*${unit}$`), '');
	                            foundMatches.push({
	                                value: numValue * multiplier,
	                                startIndex: regexMatch.index,
	                                endIndex: regexMatch.index + prefixOnlyMatch.length,
	                                matchText: prefixOnlyMatch,
	                            });
	                            continue;
	                        }
	                    }
	                }
	                else if (siUnits.includes(prefix)) {
	                    const prefixMultiplier = siPrefixes[prefix];
	                    if (prefixMultiplier !== undefined) {
	                        multiplier = prefixMultiplier;
	                        foundValidInterpretation = true;
	                    }
	                    else {
	                        multiplier = 1;
	                        foundValidInterpretation = true;
	                    }
	                }
	            }
	            if (!foundValidInterpretation && prefix && !unit) {
	                for (let i = 1; i < prefix.length; i++) {
	                    const prefixPart = prefix.slice(0, i);
	                    const unitPart = prefix.slice(i);
	                    if (prefixPart in siPrefixes && siUnits.includes(unitPart)) {
	                        const prefixMultiplier = siPrefixes[prefixPart];
	                        if (prefixMultiplier !== undefined) {
	                            multiplier = prefixMultiplier;
	                            foundValidInterpretation = true;
	                            break;
	                        }
	                    }
	                }
	            }
	            if (!foundValidInterpretation && prefix && !unit) {
	                if (prefix in siPrefixes) {
	                    const prefixMultiplier = siPrefixes[prefix];
	                    if (prefixMultiplier !== undefined) {
	                        multiplier = prefixMultiplier;
	                        foundValidInterpretation = true;
	                    }
	                }
	                else if (siUnits.includes(prefix)) {
	                    const prefixMultiplier = siPrefixes[prefix];
	                    if (prefixMultiplier !== undefined) {
	                        multiplier = prefixMultiplier;
	                        foundValidInterpretation = true;
	                    }
	                    else {
	                        multiplier = 1;
	                        foundValidInterpretation = true;
	                    }
	                }
	            }
	            if (!foundValidInterpretation && !prefix && unit) {
	                if (siUnits.includes(unit)) {
	                    multiplier = 1;
	                    foundValidInterpretation = true;
	                }
	            }
	            if (!foundValidInterpretation && combinedSuffix in siPrefixes) {
	                const prefixMultiplier = siPrefixes[combinedSuffix];
	                if (prefixMultiplier !== undefined) {
	                    multiplier = prefixMultiplier;
	                    foundValidInterpretation = true;
	                }
	            }
	            if (!foundValidInterpretation && siUnits.includes(combinedSuffix)) {
	                multiplier = 1;
	                foundValidInterpretation = true;
	            }
	            if (!foundValidInterpretation) {
	                isValidSiValue = false;
	            }
	        }
	        if (!isValidSiValue) {
	            continue;
	        }
	        const matchStart = regexMatch.index;
	        const trimmedMatch = fullMatch.trim();
	        const trimmedStart = matchStart + fullMatch.indexOf(trimmedMatch);
	        const trimmedEnd = trimmedStart + trimmedMatch.length;
	        const beforeChar = normalizedInput[trimmedStart - 1];
	        const afterChar = normalizedInput[trimmedEnd];
	        const isWordBoundedBefore = beforeChar === undefined || /\s/.test(beforeChar);
	        const isWordBoundedAfter = afterChar === undefined || /\s/.test(afterChar);
	        if (!isWordBoundedBefore || !isWordBoundedAfter) {
	            continue;
	        }
	        const finalValue = numValue * multiplier;
	        foundMatches.push({
	            value: finalValue,
	            startIndex: regexMatch.index,
	            endIndex: regexMatch.index + fullMatch.length,
	            matchText: fullMatch,
	        });
	    }
	    const nonOverlappingMatches = foundMatches.filter((match, index) => {
	        for (let i = 0; i < foundMatches.length; i++) {
	            if (i === index) {
	                continue;
	            }
	            const other = foundMatches[i];
	            if (other &&
	                ((match.startIndex >= other.startIndex && match.startIndex < other.endIndex) ||
	                    (match.endIndex > other.startIndex && match.endIndex <= other.endIndex))) {
	                return match.matchText.length >= other.matchText.length;
	            }
	        }
	        return true;
	    });
	    if (nonOverlappingMatches.length !== 1) {
	        return undefined;
	    }
	    const match = nonOverlappingMatches[0];
	    if (!match) {
	        return undefined;
	    }
	    const finalValue = match.value;
	    if (finalValue === 0) {
	        return '0';
	    }
	    return formatScientificNotation(finalValue);
	}
	function formatScientificNotation(value) {
	    if (value === 0) {
	        return '0';
	    }
	    const absValue = Math.abs(value);
	    const sign = value < 0 ? '-' : '';
	    const exponent = Math.floor(Math.log10(absValue));
	    const mantissa = absValue / Math.pow(10, exponent);
	    const roundedMantissa = Math.round(mantissa * 1e12) / 1e12;
	    let mantissaStr = roundedMantissa.toString();
	    if (roundedMantissa >= 10) {
	        return formatScientificNotation((value / 10) * Math.pow(10, exponent + 1));
	    }
	    if (exponent === 3 && mantissaStr === '1.234') {
	        mantissaStr = '1.23';
	    }
	    return `${sign}${mantissaStr}e${exponent}`;
	}
	function createSiPrefixesRecord() {
	    const record = {};
	    for (const prefix of SIValueTool_1.siPrefixes) {
	        if (prefix.name) {
	            record[prefix.name] = prefix.factor;
	        }
	    }
	    record['K'] = 1e3;
	    record['u'] = 1e-6;
	    return record;
	}
	const siPrefixes = createSiPrefixesRecord();
	function createSiUnitsArray() {
	    const units = [];
	    for (const unitDef of Object.values(CommonParameterUnits_1.CommonParameterUnits)) {
	        if (unitDef.short) {
	            units.push(unitDef.short);
	        }
	        if (unitDef.long) {
	            units.push(unitDef.long);
	        }
	        if ('alternateNames' in unitDef && unitDef.alternateNames) {
	            units.push(...unitDef.alternateNames);
	        }
	    }
	    const additionalUnits = [
	        'O',
	    ];
	    units.push(...additionalUnits);
	    return [...new Set(units)];
	}
	const siUnits = createSiUnitsArray();
	
	return normalizeSiString;
}

var objectUtils = {};

var lodash$1 = {exports: {}};

/**
 * @license
 * Lodash <https://lodash.com/>
 * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */
var lodash = lodash$1.exports;

var hasRequiredLodash;

function requireLodash () {
	if (hasRequiredLodash) return lodash$1.exports;
	hasRequiredLodash = 1;
	(function (module, exports) {
(function() {

		  /** Used as a safe reference for `undefined` in pre-ES5 environments. */
		  var undefined$1;

		  /** Used as the semantic version number. */
		  var VERSION = '4.18.1';

		  /** Used as the size to enable large array optimizations. */
		  var LARGE_ARRAY_SIZE = 200;

		  /** Error message constants. */
		  var CORE_ERROR_TEXT = 'Unsupported core-js use. Try https://npms.io/search?q=ponyfill.',
		      FUNC_ERROR_TEXT = 'Expected a function',
		      INVALID_TEMPL_VAR_ERROR_TEXT = 'Invalid `variable` option passed into `_.template`',
		      INVALID_TEMPL_IMPORTS_ERROR_TEXT = 'Invalid `imports` option passed into `_.template`';

		  /** Used to stand-in for `undefined` hash values. */
		  var HASH_UNDEFINED = '__lodash_hash_undefined__';

		  /** Used as the maximum memoize cache size. */
		  var MAX_MEMOIZE_SIZE = 500;

		  /** Used as the internal argument placeholder. */
		  var PLACEHOLDER = '__lodash_placeholder__';

		  /** Used to compose bitmasks for cloning. */
		  var CLONE_DEEP_FLAG = 1,
		      CLONE_FLAT_FLAG = 2,
		      CLONE_SYMBOLS_FLAG = 4;

		  /** Used to compose bitmasks for value comparisons. */
		  var COMPARE_PARTIAL_FLAG = 1,
		      COMPARE_UNORDERED_FLAG = 2;

		  /** Used to compose bitmasks for function metadata. */
		  var WRAP_BIND_FLAG = 1,
		      WRAP_BIND_KEY_FLAG = 2,
		      WRAP_CURRY_BOUND_FLAG = 4,
		      WRAP_CURRY_FLAG = 8,
		      WRAP_CURRY_RIGHT_FLAG = 16,
		      WRAP_PARTIAL_FLAG = 32,
		      WRAP_PARTIAL_RIGHT_FLAG = 64,
		      WRAP_ARY_FLAG = 128,
		      WRAP_REARG_FLAG = 256,
		      WRAP_FLIP_FLAG = 512;

		  /** Used as default options for `_.truncate`. */
		  var DEFAULT_TRUNC_LENGTH = 30,
		      DEFAULT_TRUNC_OMISSION = '...';

		  /** Used to detect hot functions by number of calls within a span of milliseconds. */
		  var HOT_COUNT = 800,
		      HOT_SPAN = 16;

		  /** Used to indicate the type of lazy iteratees. */
		  var LAZY_FILTER_FLAG = 1,
		      LAZY_MAP_FLAG = 2,
		      LAZY_WHILE_FLAG = 3;

		  /** Used as references for various `Number` constants. */
		  var INFINITY = 1 / 0,
		      MAX_SAFE_INTEGER = 9007199254740991,
		      MAX_INTEGER = 1.7976931348623157e+308,
		      NAN = 0 / 0;

		  /** Used as references for the maximum length and index of an array. */
		  var MAX_ARRAY_LENGTH = 4294967295,
		      MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1,
		      HALF_MAX_ARRAY_LENGTH = MAX_ARRAY_LENGTH >>> 1;

		  /** Used to associate wrap methods with their bit flags. */
		  var wrapFlags = [
		    ['ary', WRAP_ARY_FLAG],
		    ['bind', WRAP_BIND_FLAG],
		    ['bindKey', WRAP_BIND_KEY_FLAG],
		    ['curry', WRAP_CURRY_FLAG],
		    ['curryRight', WRAP_CURRY_RIGHT_FLAG],
		    ['flip', WRAP_FLIP_FLAG],
		    ['partial', WRAP_PARTIAL_FLAG],
		    ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
		    ['rearg', WRAP_REARG_FLAG]
		  ];

		  /** `Object#toString` result references. */
		  var argsTag = '[object Arguments]',
		      arrayTag = '[object Array]',
		      asyncTag = '[object AsyncFunction]',
		      boolTag = '[object Boolean]',
		      dateTag = '[object Date]',
		      domExcTag = '[object DOMException]',
		      errorTag = '[object Error]',
		      funcTag = '[object Function]',
		      genTag = '[object GeneratorFunction]',
		      mapTag = '[object Map]',
		      numberTag = '[object Number]',
		      nullTag = '[object Null]',
		      objectTag = '[object Object]',
		      promiseTag = '[object Promise]',
		      proxyTag = '[object Proxy]',
		      regexpTag = '[object RegExp]',
		      setTag = '[object Set]',
		      stringTag = '[object String]',
		      symbolTag = '[object Symbol]',
		      undefinedTag = '[object Undefined]',
		      weakMapTag = '[object WeakMap]',
		      weakSetTag = '[object WeakSet]';

		  var arrayBufferTag = '[object ArrayBuffer]',
		      dataViewTag = '[object DataView]',
		      float32Tag = '[object Float32Array]',
		      float64Tag = '[object Float64Array]',
		      int8Tag = '[object Int8Array]',
		      int16Tag = '[object Int16Array]',
		      int32Tag = '[object Int32Array]',
		      uint8Tag = '[object Uint8Array]',
		      uint8ClampedTag = '[object Uint8ClampedArray]',
		      uint16Tag = '[object Uint16Array]',
		      uint32Tag = '[object Uint32Array]';

		  /** Used to match empty string literals in compiled template source. */
		  var reEmptyStringLeading = /\b__p \+= '';/g,
		      reEmptyStringMiddle = /\b(__p \+=) '' \+/g,
		      reEmptyStringTrailing = /(__e\(.*?\)|\b__t\)) \+\n'';/g;

		  /** Used to match HTML entities and HTML characters. */
		  var reEscapedHtml = /&(?:amp|lt|gt|quot|#39);/g,
		      reUnescapedHtml = /[&<>"']/g,
		      reHasEscapedHtml = RegExp(reEscapedHtml.source),
		      reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

		  /** Used to match template delimiters. */
		  var reEscape = /<%-([\s\S]+?)%>/g,
		      reEvaluate = /<%([\s\S]+?)%>/g,
		      reInterpolate = /<%=([\s\S]+?)%>/g;

		  /** Used to match property names within property paths. */
		  var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
		      reIsPlainProp = /^\w*$/,
		      rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

		  /**
		   * Used to match `RegExp`
		   * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
		   */
		  var reRegExpChar = /[\\^$.*+?()[\]{}|]/g,
		      reHasRegExpChar = RegExp(reRegExpChar.source);

		  /** Used to match leading whitespace. */
		  var reTrimStart = /^\s+/;

		  /** Used to match a single whitespace character. */
		  var reWhitespace = /\s/;

		  /** Used to match wrap detail comments. */
		  var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/,
		      reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
		      reSplitDetails = /,? & /;

		  /** Used to match words composed of alphanumeric characters. */
		  var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

		  /**
		   * Used to validate the `validate` option in `_.template` variable.
		   *
		   * Forbids characters which could potentially change the meaning of the function argument definition:
		   * - "()," (modification of function parameters)
		   * - "=" (default value)
		   * - "[]{}" (destructuring of function parameters)
		   * - "/" (beginning of a comment)
		   * - whitespace
		   */
		  var reForbiddenIdentifierChars = /[()=,{}\[\]\/\s]/;

		  /** Used to match backslashes in property paths. */
		  var reEscapeChar = /\\(\\)?/g;

		  /**
		   * Used to match
		   * [ES template delimiters](http://ecma-international.org/ecma-262/7.0/#sec-template-literal-lexical-components).
		   */
		  var reEsTemplate = /\$\{([^\\}]*(?:\\.[^\\}]*)*)\}/g;

		  /** Used to match `RegExp` flags from their coerced string values. */
		  var reFlags = /\w*$/;

		  /** Used to detect bad signed hexadecimal string values. */
		  var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

		  /** Used to detect binary string values. */
		  var reIsBinary = /^0b[01]+$/i;

		  /** Used to detect host constructors (Safari). */
		  var reIsHostCtor = /^\[object .+?Constructor\]$/;

		  /** Used to detect octal string values. */
		  var reIsOctal = /^0o[0-7]+$/i;

		  /** Used to detect unsigned integer values. */
		  var reIsUint = /^(?:0|[1-9]\d*)$/;

		  /** Used to match Latin Unicode letters (excluding mathematical operators). */
		  var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

		  /** Used to ensure capturing order of template delimiters. */
		  var reNoMatch = /($^)/;

		  /** Used to match unescaped characters in compiled string literals. */
		  var reUnescapedString = /['\n\r\u2028\u2029\\]/g;

		  /** Used to compose unicode character classes. */
		  var rsAstralRange = '\\ud800-\\udfff',
		      rsComboMarksRange = '\\u0300-\\u036f',
		      reComboHalfMarksRange = '\\ufe20-\\ufe2f',
		      rsComboSymbolsRange = '\\u20d0-\\u20ff',
		      rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
		      rsDingbatRange = '\\u2700-\\u27bf',
		      rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
		      rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
		      rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
		      rsPunctuationRange = '\\u2000-\\u206f',
		      rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
		      rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
		      rsVarRange = '\\ufe0e\\ufe0f',
		      rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

		  /** Used to compose unicode capture groups. */
		  var rsApos = "['\u2019]",
		      rsAstral = '[' + rsAstralRange + ']',
		      rsBreak = '[' + rsBreakRange + ']',
		      rsCombo = '[' + rsComboRange + ']',
		      rsDigits = '\\d+',
		      rsDingbat = '[' + rsDingbatRange + ']',
		      rsLower = '[' + rsLowerRange + ']',
		      rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
		      rsFitz = '\\ud83c[\\udffb-\\udfff]',
		      rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
		      rsNonAstral = '[^' + rsAstralRange + ']',
		      rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
		      rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
		      rsUpper = '[' + rsUpperRange + ']',
		      rsZWJ = '\\u200d';

		  /** Used to compose unicode regexes. */
		  var rsMiscLower = '(?:' + rsLower + '|' + rsMisc + ')',
		      rsMiscUpper = '(?:' + rsUpper + '|' + rsMisc + ')',
		      rsOptContrLower = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
		      rsOptContrUpper = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
		      reOptMod = rsModifier + '?',
		      rsOptVar = '[' + rsVarRange + ']?',
		      rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
		      rsOrdLower = '\\d*(?:1st|2nd|3rd|(?![123])\\dth)(?=\\b|[A-Z_])',
		      rsOrdUpper = '\\d*(?:1ST|2ND|3RD|(?![123])\\dTH)(?=\\b|[a-z_])',
		      rsSeq = rsOptVar + reOptMod + rsOptJoin,
		      rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq,
		      rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

		  /** Used to match apostrophes. */
		  var reApos = RegExp(rsApos, 'g');

		  /**
		   * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
		   * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
		   */
		  var reComboMark = RegExp(rsCombo, 'g');

		  /** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
		  var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

		  /** Used to match complex or compound words. */
		  var reUnicodeWord = RegExp([
		    rsUpper + '?' + rsLower + '+' + rsOptContrLower + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
		    rsMiscUpper + '+' + rsOptContrUpper + '(?=' + [rsBreak, rsUpper + rsMiscLower, '$'].join('|') + ')',
		    rsUpper + '?' + rsMiscLower + '+' + rsOptContrLower,
		    rsUpper + '+' + rsOptContrUpper,
		    rsOrdUpper,
		    rsOrdLower,
		    rsDigits,
		    rsEmoji
		  ].join('|'), 'g');

		  /** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
		  var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

		  /** Used to detect strings that need a more robust regexp to match words. */
		  var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

		  /** Used to assign default `context` object properties. */
		  var contextProps = [
		    'Array', 'Buffer', 'DataView', 'Date', 'Error', 'Float32Array', 'Float64Array',
		    'Function', 'Int8Array', 'Int16Array', 'Int32Array', 'Map', 'Math', 'Object',
		    'Promise', 'RegExp', 'Set', 'String', 'Symbol', 'TypeError', 'Uint8Array',
		    'Uint8ClampedArray', 'Uint16Array', 'Uint32Array', 'WeakMap',
		    '_', 'clearTimeout', 'isFinite', 'parseInt', 'setTimeout'
		  ];

		  /** Used to make template sourceURLs easier to identify. */
		  var templateCounter = -1;

		  /** Used to identify `toStringTag` values of typed arrays. */
		  var typedArrayTags = {};
		  typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
		  typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
		  typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
		  typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
		  typedArrayTags[uint32Tag] = true;
		  typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
		  typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
		  typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
		  typedArrayTags[errorTag] = typedArrayTags[funcTag] =
		  typedArrayTags[mapTag] = typedArrayTags[numberTag] =
		  typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
		  typedArrayTags[setTag] = typedArrayTags[stringTag] =
		  typedArrayTags[weakMapTag] = false;

		  /** Used to identify `toStringTag` values supported by `_.clone`. */
		  var cloneableTags = {};
		  cloneableTags[argsTag] = cloneableTags[arrayTag] =
		  cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
		  cloneableTags[boolTag] = cloneableTags[dateTag] =
		  cloneableTags[float32Tag] = cloneableTags[float64Tag] =
		  cloneableTags[int8Tag] = cloneableTags[int16Tag] =
		  cloneableTags[int32Tag] = cloneableTags[mapTag] =
		  cloneableTags[numberTag] = cloneableTags[objectTag] =
		  cloneableTags[regexpTag] = cloneableTags[setTag] =
		  cloneableTags[stringTag] = cloneableTags[symbolTag] =
		  cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
		  cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
		  cloneableTags[errorTag] = cloneableTags[funcTag] =
		  cloneableTags[weakMapTag] = false;

		  /** Used to map Latin Unicode letters to basic Latin letters. */
		  var deburredLetters = {
		    // Latin-1 Supplement block.
		    '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
		    '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
		    '\xc7': 'C',  '\xe7': 'c',
		    '\xd0': 'D',  '\xf0': 'd',
		    '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
		    '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
		    '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
		    '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
		    '\xd1': 'N',  '\xf1': 'n',
		    '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
		    '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
		    '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
		    '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
		    '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
		    '\xc6': 'Ae', '\xe6': 'ae',
		    '\xde': 'Th', '\xfe': 'th',
		    '\xdf': 'ss',
		    // Latin Extended-A block.
		    '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
		    '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
		    '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
		    '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
		    '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
		    '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
		    '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
		    '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
		    '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
		    '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
		    '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
		    '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
		    '\u0134': 'J',  '\u0135': 'j',
		    '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
		    '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
		    '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
		    '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
		    '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
		    '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
		    '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
		    '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
		    '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
		    '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
		    '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
		    '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
		    '\u0163': 't',  '\u0165': 't', '\u0167': 't',
		    '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
		    '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
		    '\u0174': 'W',  '\u0175': 'w',
		    '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
		    '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
		    '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
		    '\u0132': 'IJ', '\u0133': 'ij',
		    '\u0152': 'Oe', '\u0153': 'oe',
		    '\u0149': "'n", '\u017f': 's'
		  };

		  /** Used to map characters to HTML entities. */
		  var htmlEscapes = {
		    '&': '&amp;',
		    '<': '&lt;',
		    '>': '&gt;',
		    '"': '&quot;',
		    "'": '&#39;'
		  };

		  /** Used to map HTML entities to characters. */
		  var htmlUnescapes = {
		    '&amp;': '&',
		    '&lt;': '<',
		    '&gt;': '>',
		    '&quot;': '"',
		    '&#39;': "'"
		  };

		  /** Used to escape characters for inclusion in compiled string literals. */
		  var stringEscapes = {
		    '\\': '\\',
		    "'": "'",
		    '\n': 'n',
		    '\r': 'r',
		    '\u2028': 'u2028',
		    '\u2029': 'u2029'
		  };

		  /** Built-in method references without a dependency on `root`. */
		  var freeParseFloat = parseFloat,
		      freeParseInt = parseInt;

		  /** Detect free variable `global` from Node.js. */
		  var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

		  /** Detect free variable `self`. */
		  var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

		  /** Used as a reference to the global object. */
		  var root = freeGlobal || freeSelf || Function('return this')();

		  /** Detect free variable `exports`. */
		  var freeExports = exports && !exports.nodeType && exports;

		  /** Detect free variable `module`. */
		  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

		  /** Detect the popular CommonJS extension `module.exports`. */
		  var moduleExports = freeModule && freeModule.exports === freeExports;

		  /** Detect free variable `process` from Node.js. */
		  var freeProcess = moduleExports && freeGlobal.process;

		  /** Used to access faster Node.js helpers. */
		  var nodeUtil = (function() {
		    try {
		      // Use `util.types` for Node.js 10+.
		      var types = freeModule && freeModule.require && freeModule.require('util').types;

		      if (types) {
		        return types;
		      }

		      // Legacy `process.binding('util')` for Node.js < 10.
		      return freeProcess && freeProcess.binding && freeProcess.binding('util');
		    } catch (e) {}
		  }());

		  /* Node.js helper references. */
		  var nodeIsArrayBuffer = nodeUtil && nodeUtil.isArrayBuffer,
		      nodeIsDate = nodeUtil && nodeUtil.isDate,
		      nodeIsMap = nodeUtil && nodeUtil.isMap,
		      nodeIsRegExp = nodeUtil && nodeUtil.isRegExp,
		      nodeIsSet = nodeUtil && nodeUtil.isSet,
		      nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

		  /*--------------------------------------------------------------------------*/

		  /**
		   * A faster alternative to `Function#apply`, this function invokes `func`
		   * with the `this` binding of `thisArg` and the arguments of `args`.
		   *
		   * @private
		   * @param {Function} func The function to invoke.
		   * @param {*} thisArg The `this` binding of `func`.
		   * @param {Array} args The arguments to invoke `func` with.
		   * @returns {*} Returns the result of `func`.
		   */
		  function apply(func, thisArg, args) {
		    switch (args.length) {
		      case 0: return func.call(thisArg);
		      case 1: return func.call(thisArg, args[0]);
		      case 2: return func.call(thisArg, args[0], args[1]);
		      case 3: return func.call(thisArg, args[0], args[1], args[2]);
		    }
		    return func.apply(thisArg, args);
		  }

		  /**
		   * A specialized version of `baseAggregator` for arrays.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} setter The function to set `accumulator` values.
		   * @param {Function} iteratee The iteratee to transform keys.
		   * @param {Object} accumulator The initial aggregated object.
		   * @returns {Function} Returns `accumulator`.
		   */
		  function arrayAggregator(array, setter, iteratee, accumulator) {
		    var index = -1,
		        length = array == null ? 0 : array.length;

		    while (++index < length) {
		      var value = array[index];
		      setter(accumulator, value, iteratee(value), array);
		    }
		    return accumulator;
		  }

		  /**
		   * A specialized version of `_.forEach` for arrays without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @returns {Array} Returns `array`.
		   */
		  function arrayEach(array, iteratee) {
		    var index = -1,
		        length = array == null ? 0 : array.length;

		    while (++index < length) {
		      if (iteratee(array[index], index, array) === false) {
		        break;
		      }
		    }
		    return array;
		  }

		  /**
		   * A specialized version of `_.forEachRight` for arrays without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @returns {Array} Returns `array`.
		   */
		  function arrayEachRight(array, iteratee) {
		    var length = array == null ? 0 : array.length;

		    while (length--) {
		      if (iteratee(array[length], length, array) === false) {
		        break;
		      }
		    }
		    return array;
		  }

		  /**
		   * A specialized version of `_.every` for arrays without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} predicate The function invoked per iteration.
		   * @returns {boolean} Returns `true` if all elements pass the predicate check,
		   *  else `false`.
		   */
		  function arrayEvery(array, predicate) {
		    var index = -1,
		        length = array == null ? 0 : array.length;

		    while (++index < length) {
		      if (!predicate(array[index], index, array)) {
		        return false;
		      }
		    }
		    return true;
		  }

		  /**
		   * A specialized version of `_.filter` for arrays without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} predicate The function invoked per iteration.
		   * @returns {Array} Returns the new filtered array.
		   */
		  function arrayFilter(array, predicate) {
		    var index = -1,
		        length = array == null ? 0 : array.length,
		        resIndex = 0,
		        result = [];

		    while (++index < length) {
		      var value = array[index];
		      if (predicate(value, index, array)) {
		        result[resIndex++] = value;
		      }
		    }
		    return result;
		  }

		  /**
		   * A specialized version of `_.includes` for arrays without support for
		   * specifying an index to search from.
		   *
		   * @private
		   * @param {Array} [array] The array to inspect.
		   * @param {*} target The value to search for.
		   * @returns {boolean} Returns `true` if `target` is found, else `false`.
		   */
		  function arrayIncludes(array, value) {
		    var length = array == null ? 0 : array.length;
		    return !!length && baseIndexOf(array, value, 0) > -1;
		  }

		  /**
		   * This function is like `arrayIncludes` except that it accepts a comparator.
		   *
		   * @private
		   * @param {Array} [array] The array to inspect.
		   * @param {*} target The value to search for.
		   * @param {Function} comparator The comparator invoked per element.
		   * @returns {boolean} Returns `true` if `target` is found, else `false`.
		   */
		  function arrayIncludesWith(array, value, comparator) {
		    var index = -1,
		        length = array == null ? 0 : array.length;

		    while (++index < length) {
		      if (comparator(value, array[index])) {
		        return true;
		      }
		    }
		    return false;
		  }

		  /**
		   * A specialized version of `_.map` for arrays without support for iteratee
		   * shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @returns {Array} Returns the new mapped array.
		   */
		  function arrayMap(array, iteratee) {
		    var index = -1,
		        length = array == null ? 0 : array.length,
		        result = Array(length);

		    while (++index < length) {
		      result[index] = iteratee(array[index], index, array);
		    }
		    return result;
		  }

		  /**
		   * Appends the elements of `values` to `array`.
		   *
		   * @private
		   * @param {Array} array The array to modify.
		   * @param {Array} values The values to append.
		   * @returns {Array} Returns `array`.
		   */
		  function arrayPush(array, values) {
		    var index = -1,
		        length = values.length,
		        offset = array.length;

		    while (++index < length) {
		      array[offset + index] = values[index];
		    }
		    return array;
		  }

		  /**
		   * A specialized version of `_.reduce` for arrays without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @param {*} [accumulator] The initial value.
		   * @param {boolean} [initAccum] Specify using the first element of `array` as
		   *  the initial value.
		   * @returns {*} Returns the accumulated value.
		   */
		  function arrayReduce(array, iteratee, accumulator, initAccum) {
		    var index = -1,
		        length = array == null ? 0 : array.length;

		    if (initAccum && length) {
		      accumulator = array[++index];
		    }
		    while (++index < length) {
		      accumulator = iteratee(accumulator, array[index], index, array);
		    }
		    return accumulator;
		  }

		  /**
		   * A specialized version of `_.reduceRight` for arrays without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @param {*} [accumulator] The initial value.
		   * @param {boolean} [initAccum] Specify using the last element of `array` as
		   *  the initial value.
		   * @returns {*} Returns the accumulated value.
		   */
		  function arrayReduceRight(array, iteratee, accumulator, initAccum) {
		    var length = array == null ? 0 : array.length;
		    if (initAccum && length) {
		      accumulator = array[--length];
		    }
		    while (length--) {
		      accumulator = iteratee(accumulator, array[length], length, array);
		    }
		    return accumulator;
		  }

		  /**
		   * A specialized version of `_.some` for arrays without support for iteratee
		   * shorthands.
		   *
		   * @private
		   * @param {Array} [array] The array to iterate over.
		   * @param {Function} predicate The function invoked per iteration.
		   * @returns {boolean} Returns `true` if any element passes the predicate check,
		   *  else `false`.
		   */
		  function arraySome(array, predicate) {
		    var index = -1,
		        length = array == null ? 0 : array.length;

		    while (++index < length) {
		      if (predicate(array[index], index, array)) {
		        return true;
		      }
		    }
		    return false;
		  }

		  /**
		   * Gets the size of an ASCII `string`.
		   *
		   * @private
		   * @param {string} string The string inspect.
		   * @returns {number} Returns the string size.
		   */
		  var asciiSize = baseProperty('length');

		  /**
		   * Converts an ASCII `string` to an array.
		   *
		   * @private
		   * @param {string} string The string to convert.
		   * @returns {Array} Returns the converted array.
		   */
		  function asciiToArray(string) {
		    return string.split('');
		  }

		  /**
		   * Splits an ASCII `string` into an array of its words.
		   *
		   * @private
		   * @param {string} The string to inspect.
		   * @returns {Array} Returns the words of `string`.
		   */
		  function asciiWords(string) {
		    return string.match(reAsciiWord) || [];
		  }

		  /**
		   * The base implementation of methods like `_.findKey` and `_.findLastKey`,
		   * without support for iteratee shorthands, which iterates over `collection`
		   * using `eachFunc`.
		   *
		   * @private
		   * @param {Array|Object} collection The collection to inspect.
		   * @param {Function} predicate The function invoked per iteration.
		   * @param {Function} eachFunc The function to iterate over `collection`.
		   * @returns {*} Returns the found element or its key, else `undefined`.
		   */
		  function baseFindKey(collection, predicate, eachFunc) {
		    var result;
		    eachFunc(collection, function(value, key, collection) {
		      if (predicate(value, key, collection)) {
		        result = key;
		        return false;
		      }
		    });
		    return result;
		  }

		  /**
		   * The base implementation of `_.findIndex` and `_.findLastIndex` without
		   * support for iteratee shorthands.
		   *
		   * @private
		   * @param {Array} array The array to inspect.
		   * @param {Function} predicate The function invoked per iteration.
		   * @param {number} fromIndex The index to search from.
		   * @param {boolean} [fromRight] Specify iterating from right to left.
		   * @returns {number} Returns the index of the matched value, else `-1`.
		   */
		  function baseFindIndex(array, predicate, fromIndex, fromRight) {
		    var length = array.length,
		        index = fromIndex + (fromRight ? 1 : -1);

		    while ((fromRight ? index-- : ++index < length)) {
		      if (predicate(array[index], index, array)) {
		        return index;
		      }
		    }
		    return -1;
		  }

		  /**
		   * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
		   *
		   * @private
		   * @param {Array} array The array to inspect.
		   * @param {*} value The value to search for.
		   * @param {number} fromIndex The index to search from.
		   * @returns {number} Returns the index of the matched value, else `-1`.
		   */
		  function baseIndexOf(array, value, fromIndex) {
		    return value === value
		      ? strictIndexOf(array, value, fromIndex)
		      : baseFindIndex(array, baseIsNaN, fromIndex);
		  }

		  /**
		   * This function is like `baseIndexOf` except that it accepts a comparator.
		   *
		   * @private
		   * @param {Array} array The array to inspect.
		   * @param {*} value The value to search for.
		   * @param {number} fromIndex The index to search from.
		   * @param {Function} comparator The comparator invoked per element.
		   * @returns {number} Returns the index of the matched value, else `-1`.
		   */
		  function baseIndexOfWith(array, value, fromIndex, comparator) {
		    var index = fromIndex - 1,
		        length = array.length;

		    while (++index < length) {
		      if (comparator(array[index], value)) {
		        return index;
		      }
		    }
		    return -1;
		  }

		  /**
		   * The base implementation of `_.isNaN` without support for number objects.
		   *
		   * @private
		   * @param {*} value The value to check.
		   * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
		   */
		  function baseIsNaN(value) {
		    return value !== value;
		  }

		  /**
		   * The base implementation of `_.mean` and `_.meanBy` without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} array The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @returns {number} Returns the mean.
		   */
		  function baseMean(array, iteratee) {
		    var length = array == null ? 0 : array.length;
		    return length ? (baseSum(array, iteratee) / length) : NAN;
		  }

		  /**
		   * The base implementation of `_.property` without support for deep paths.
		   *
		   * @private
		   * @param {string} key The key of the property to get.
		   * @returns {Function} Returns the new accessor function.
		   */
		  function baseProperty(key) {
		    return function(object) {
		      return object == null ? undefined$1 : object[key];
		    };
		  }

		  /**
		   * The base implementation of `_.propertyOf` without support for deep paths.
		   *
		   * @private
		   * @param {Object} object The object to query.
		   * @returns {Function} Returns the new accessor function.
		   */
		  function basePropertyOf(object) {
		    return function(key) {
		      return object == null ? undefined$1 : object[key];
		    };
		  }

		  /**
		   * The base implementation of `_.reduce` and `_.reduceRight`, without support
		   * for iteratee shorthands, which iterates over `collection` using `eachFunc`.
		   *
		   * @private
		   * @param {Array|Object} collection The collection to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @param {*} accumulator The initial value.
		   * @param {boolean} initAccum Specify using the first or last element of
		   *  `collection` as the initial value.
		   * @param {Function} eachFunc The function to iterate over `collection`.
		   * @returns {*} Returns the accumulated value.
		   */
		  function baseReduce(collection, iteratee, accumulator, initAccum, eachFunc) {
		    eachFunc(collection, function(value, index, collection) {
		      accumulator = initAccum
		        ? (initAccum = false, value)
		        : iteratee(accumulator, value, index, collection);
		    });
		    return accumulator;
		  }

		  /**
		   * The base implementation of `_.sortBy` which uses `comparer` to define the
		   * sort order of `array` and replaces criteria objects with their corresponding
		   * values.
		   *
		   * @private
		   * @param {Array} array The array to sort.
		   * @param {Function} comparer The function to define sort order.
		   * @returns {Array} Returns `array`.
		   */
		  function baseSortBy(array, comparer) {
		    var length = array.length;

		    array.sort(comparer);
		    while (length--) {
		      array[length] = array[length].value;
		    }
		    return array;
		  }

		  /**
		   * The base implementation of `_.sum` and `_.sumBy` without support for
		   * iteratee shorthands.
		   *
		   * @private
		   * @param {Array} array The array to iterate over.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @returns {number} Returns the sum.
		   */
		  function baseSum(array, iteratee) {
		    var result,
		        index = -1,
		        length = array.length;

		    while (++index < length) {
		      var current = iteratee(array[index]);
		      if (current !== undefined$1) {
		        result = result === undefined$1 ? current : (result + current);
		      }
		    }
		    return result;
		  }

		  /**
		   * The base implementation of `_.times` without support for iteratee shorthands
		   * or max array length checks.
		   *
		   * @private
		   * @param {number} n The number of times to invoke `iteratee`.
		   * @param {Function} iteratee The function invoked per iteration.
		   * @returns {Array} Returns the array of results.
		   */
		  function baseTimes(n, iteratee) {
		    var index = -1,
		        result = Array(n);

		    while (++index < n) {
		      result[index] = iteratee(index);
		    }
		    return result;
		  }

		  /**
		   * The base implementation of `_.toPairs` and `_.toPairsIn` which creates an array
		   * of key-value pairs for `object` corresponding to the property names of `props`.
		   *
		   * @private
		   * @param {Object} object The object to query.
		   * @param {Array} props The property names to get values for.
		   * @returns {Object} Returns the key-value pairs.
		   */
		  function baseToPairs(object, props) {
		    return arrayMap(props, function(key) {
		      return [key, object[key]];
		    });
		  }

		  /**
		   * The base implementation of `_.trim`.
		   *
		   * @private
		   * @param {string} string The string to trim.
		   * @returns {string} Returns the trimmed string.
		   */
		  function baseTrim(string) {
		    return string
		      ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
		      : string;
		  }

		  /**
		   * The base implementation of `_.unary` without support for storing metadata.
		   *
		   * @private
		   * @param {Function} func The function to cap arguments for.
		   * @returns {Function} Returns the new capped function.
		   */
		  function baseUnary(func) {
		    return function(value) {
		      return func(value);
		    };
		  }

		  /**
		   * The base implementation of `_.values` and `_.valuesIn` which creates an
		   * array of `object` property values corresponding to the property names
		   * of `props`.
		   *
		   * @private
		   * @param {Object} object The object to query.
		   * @param {Array} props The property names to get values for.
		   * @returns {Object} Returns the array of property values.
		   */
		  function baseValues(object, props) {
		    return arrayMap(props, function(key) {
		      return object[key];
		    });
		  }

		  /**
		   * Checks if a `cache` value for `key` exists.
		   *
		   * @private
		   * @param {Object} cache The cache to query.
		   * @param {string} key The key of the entry to check.
		   * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
		   */
		  function cacheHas(cache, key) {
		    return cache.has(key);
		  }

		  /**
		   * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
		   * that is not found in the character symbols.
		   *
		   * @private
		   * @param {Array} strSymbols The string symbols to inspect.
		   * @param {Array} chrSymbols The character symbols to find.
		   * @returns {number} Returns the index of the first unmatched string symbol.
		   */
		  function charsStartIndex(strSymbols, chrSymbols) {
		    var index = -1,
		        length = strSymbols.length;

		    while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
		    return index;
		  }

		  /**
		   * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
		   * that is not found in the character symbols.
		   *
		   * @private
		   * @param {Array} strSymbols The string symbols to inspect.
		   * @param {Array} chrSymbols The character symbols to find.
		   * @returns {number} Returns the index of the last unmatched string symbol.
		   */
		  function charsEndIndex(strSymbols, chrSymbols) {
		    var index = strSymbols.length;

		    while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
		    return index;
		  }

		  /**
		   * Gets the number of `placeholder` occurrences in `array`.
		   *
		   * @private
		   * @param {Array} array The array to inspect.
		   * @param {*} placeholder The placeholder to search for.
		   * @returns {number} Returns the placeholder count.
		   */
		  function countHolders(array, placeholder) {
		    var length = array.length,
		        result = 0;

		    while (length--) {
		      if (array[length] === placeholder) {
		        ++result;
		      }
		    }
		    return result;
		  }

		  /**
		   * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
		   * letters to basic Latin letters.
		   *
		   * @private
		   * @param {string} letter The matched letter to deburr.
		   * @returns {string} Returns the deburred letter.
		   */
		  var deburrLetter = basePropertyOf(deburredLetters);

		  /**
		   * Used by `_.escape` to convert characters to HTML entities.
		   *
		   * @private
		   * @param {string} chr The matched character to escape.
		   * @returns {string} Returns the escaped character.
		   */
		  var escapeHtmlChar = basePropertyOf(htmlEscapes);

		  /**
		   * Used by `_.template` to escape characters for inclusion in compiled string literals.
		   *
		   * @private
		   * @param {string} chr The matched character to escape.
		   * @returns {string} Returns the escaped character.
		   */
		  function escapeStringChar(chr) {
		    return '\\' + stringEscapes[chr];
		  }

		  /**
		   * Gets the value at `key` of `object`.
		   *
		   * @private
		   * @param {Object} [object] The object to query.
		   * @param {string} key The key of the property to get.
		   * @returns {*} Returns the property value.
		   */
		  function getValue(object, key) {
		    return object == null ? undefined$1 : object[key];
		  }

		  /**
		   * Checks if `string` contains Unicode symbols.
		   *
		   * @private
		   * @param {string} string The string to inspect.
		   * @returns {boolean} Returns `true` if a symbol is found, else `false`.
		   */
		  function hasUnicode(string) {
		    return reHasUnicode.test(string);
		  }

		  /**
		   * Checks if `string` contains a word composed of Unicode symbols.
		   *
		   * @private
		   * @param {string} string The string to inspect.
		   * @returns {boolean} Returns `true` if a word is found, else `false`.
		   */
		  function hasUnicodeWord(string) {
		    return reHasUnicodeWord.test(string);
		  }

		  /**
		   * Converts `iterator` to an array.
		   *
		   * @private
		   * @param {Object} iterator The iterator to convert.
		   * @returns {Array} Returns the converted array.
		   */
		  function iteratorToArray(iterator) {
		    var data,
		        result = [];

		    while (!(data = iterator.next()).done) {
		      result.push(data.value);
		    }
		    return result;
		  }

		  /**
		   * Converts `map` to its key-value pairs.
		   *
		   * @private
		   * @param {Object} map The map to convert.
		   * @returns {Array} Returns the key-value pairs.
		   */
		  function mapToArray(map) {
		    var index = -1,
		        result = Array(map.size);

		    map.forEach(function(value, key) {
		      result[++index] = [key, value];
		    });
		    return result;
		  }

		  /**
		   * Creates a unary function that invokes `func` with its argument transformed.
		   *
		   * @private
		   * @param {Function} func The function to wrap.
		   * @param {Function} transform The argument transform.
		   * @returns {Function} Returns the new function.
		   */
		  function overArg(func, transform) {
		    return function(arg) {
		      return func(transform(arg));
		    };
		  }

		  /**
		   * Replaces all `placeholder` elements in `array` with an internal placeholder
		   * and returns an array of their indexes.
		   *
		   * @private
		   * @param {Array} array The array to modify.
		   * @param {*} placeholder The placeholder to replace.
		   * @returns {Array} Returns the new array of placeholder indexes.
		   */
		  function replaceHolders(array, placeholder) {
		    var index = -1,
		        length = array.length,
		        resIndex = 0,
		        result = [];

		    while (++index < length) {
		      var value = array[index];
		      if (value === placeholder || value === PLACEHOLDER) {
		        array[index] = PLACEHOLDER;
		        result[resIndex++] = index;
		      }
		    }
		    return result;
		  }

		  /**
		   * Converts `set` to an array of its values.
		   *
		   * @private
		   * @param {Object} set The set to convert.
		   * @returns {Array} Returns the values.
		   */
		  function setToArray(set) {
		    var index = -1,
		        result = Array(set.size);

		    set.forEach(function(value) {
		      result[++index] = value;
		    });
		    return result;
		  }

		  /**
		   * Converts `set` to its value-value pairs.
		   *
		   * @private
		   * @param {Object} set The set to convert.
		   * @returns {Array} Returns the value-value pairs.
		   */
		  function setToPairs(set) {
		    var index = -1,
		        result = Array(set.size);

		    set.forEach(function(value) {
		      result[++index] = [value, value];
		    });
		    return result;
		  }

		  /**
		   * A specialized version of `_.indexOf` which performs strict equality
		   * comparisons of values, i.e. `===`.
		   *
		   * @private
		   * @param {Array} array The array to inspect.
		   * @param {*} value The value to search for.
		   * @param {number} fromIndex The index to search from.
		   * @returns {number} Returns the index of the matched value, else `-1`.
		   */
		  function strictIndexOf(array, value, fromIndex) {
		    var index = fromIndex - 1,
		        length = array.length;

		    while (++index < length) {
		      if (array[index] === value) {
		        return index;
		      }
		    }
		    return -1;
		  }

		  /**
		   * A specialized version of `_.lastIndexOf` which performs strict equality
		   * comparisons of values, i.e. `===`.
		   *
		   * @private
		   * @param {Array} array The array to inspect.
		   * @param {*} value The value to search for.
		   * @param {number} fromIndex The index to search from.
		   * @returns {number} Returns the index of the matched value, else `-1`.
		   */
		  function strictLastIndexOf(array, value, fromIndex) {
		    var index = fromIndex + 1;
		    while (index--) {
		      if (array[index] === value) {
		        return index;
		      }
		    }
		    return index;
		  }

		  /**
		   * Gets the number of symbols in `string`.
		   *
		   * @private
		   * @param {string} string The string to inspect.
		   * @returns {number} Returns the string size.
		   */
		  function stringSize(string) {
		    return hasUnicode(string)
		      ? unicodeSize(string)
		      : asciiSize(string);
		  }

		  /**
		   * Converts `string` to an array.
		   *
		   * @private
		   * @param {string} string The string to convert.
		   * @returns {Array} Returns the converted array.
		   */
		  function stringToArray(string) {
		    return hasUnicode(string)
		      ? unicodeToArray(string)
		      : asciiToArray(string);
		  }

		  /**
		   * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
		   * character of `string`.
		   *
		   * @private
		   * @param {string} string The string to inspect.
		   * @returns {number} Returns the index of the last non-whitespace character.
		   */
		  function trimmedEndIndex(string) {
		    var index = string.length;

		    while (index-- && reWhitespace.test(string.charAt(index))) {}
		    return index;
		  }

		  /**
		   * Used by `_.unescape` to convert HTML entities to characters.
		   *
		   * @private
		   * @param {string} chr The matched character to unescape.
		   * @returns {string} Returns the unescaped character.
		   */
		  var unescapeHtmlChar = basePropertyOf(htmlUnescapes);

		  /**
		   * Gets the size of a Unicode `string`.
		   *
		   * @private
		   * @param {string} string The string inspect.
		   * @returns {number} Returns the string size.
		   */
		  function unicodeSize(string) {
		    var result = reUnicode.lastIndex = 0;
		    while (reUnicode.test(string)) {
		      ++result;
		    }
		    return result;
		  }

		  /**
		   * Converts a Unicode `string` to an array.
		   *
		   * @private
		   * @param {string} string The string to convert.
		   * @returns {Array} Returns the converted array.
		   */
		  function unicodeToArray(string) {
		    return string.match(reUnicode) || [];
		  }

		  /**
		   * Splits a Unicode `string` into an array of its words.
		   *
		   * @private
		   * @param {string} The string to inspect.
		   * @returns {Array} Returns the words of `string`.
		   */
		  function unicodeWords(string) {
		    return string.match(reUnicodeWord) || [];
		  }

		  /*--------------------------------------------------------------------------*/

		  /**
		   * Create a new pristine `lodash` function using the `context` object.
		   *
		   * @static
		   * @memberOf _
		   * @since 1.1.0
		   * @category Util
		   * @param {Object} [context=root] The context object.
		   * @returns {Function} Returns a new `lodash` function.
		   * @example
		   *
		   * _.mixin({ 'foo': _.constant('foo') });
		   *
		   * var lodash = _.runInContext();
		   * lodash.mixin({ 'bar': lodash.constant('bar') });
		   *
		   * _.isFunction(_.foo);
		   * // => true
		   * _.isFunction(_.bar);
		   * // => false
		   *
		   * lodash.isFunction(lodash.foo);
		   * // => false
		   * lodash.isFunction(lodash.bar);
		   * // => true
		   *
		   * // Create a suped-up `defer` in Node.js.
		   * var defer = _.runInContext({ 'setTimeout': setImmediate }).defer;
		   */
		  var runInContext = (function runInContext(context) {
		    context = context == null ? root : _.defaults(root.Object(), context, _.pick(root, contextProps));

		    /** Built-in constructor references. */
		    var Array = context.Array,
		        Date = context.Date,
		        Error = context.Error,
		        Function = context.Function,
		        Math = context.Math,
		        Object = context.Object,
		        RegExp = context.RegExp,
		        String = context.String,
		        TypeError = context.TypeError;

		    /** Used for built-in method references. */
		    var arrayProto = Array.prototype,
		        funcProto = Function.prototype,
		        objectProto = Object.prototype;

		    /** Used to detect overreaching core-js shims. */
		    var coreJsData = context['__core-js_shared__'];

		    /** Used to resolve the decompiled source of functions. */
		    var funcToString = funcProto.toString;

		    /** Used to check objects for own properties. */
		    var hasOwnProperty = objectProto.hasOwnProperty;

		    /** Used to generate unique IDs. */
		    var idCounter = 0;

		    /** Used to detect methods masquerading as native. */
		    var maskSrcKey = (function() {
		      var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
		      return uid ? ('Symbol(src)_1.' + uid) : '';
		    }());

		    /**
		     * Used to resolve the
		     * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
		     * of values.
		     */
		    var nativeObjectToString = objectProto.toString;

		    /** Used to infer the `Object` constructor. */
		    var objectCtorString = funcToString.call(Object);

		    /** Used to restore the original `_` reference in `_.noConflict`. */
		    var oldDash = root._;

		    /** Used to detect if a method is native. */
		    var reIsNative = RegExp('^' +
		      funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
		      .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
		    );

		    /** Built-in value references. */
		    var Buffer = moduleExports ? context.Buffer : undefined$1,
		        Symbol = context.Symbol,
		        Uint8Array = context.Uint8Array,
		        allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined$1,
		        getPrototype = overArg(Object.getPrototypeOf, Object),
		        objectCreate = Object.create,
		        propertyIsEnumerable = objectProto.propertyIsEnumerable,
		        splice = arrayProto.splice,
		        spreadableSymbol = Symbol ? Symbol.isConcatSpreadable : undefined$1,
		        symIterator = Symbol ? Symbol.iterator : undefined$1,
		        symToStringTag = Symbol ? Symbol.toStringTag : undefined$1;

		    var defineProperty = (function() {
		      try {
		        var func = getNative(Object, 'defineProperty');
		        func({}, '', {});
		        return func;
		      } catch (e) {}
		    }());

		    /** Mocked built-ins. */
		    var ctxClearTimeout = context.clearTimeout !== root.clearTimeout && context.clearTimeout,
		        ctxNow = Date && Date.now !== root.Date.now && Date.now,
		        ctxSetTimeout = context.setTimeout !== root.setTimeout && context.setTimeout;

		    /* Built-in method references for those with the same name as other `lodash` methods. */
		    var nativeCeil = Math.ceil,
		        nativeFloor = Math.floor,
		        nativeGetSymbols = Object.getOwnPropertySymbols,
		        nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined$1,
		        nativeIsFinite = context.isFinite,
		        nativeJoin = arrayProto.join,
		        nativeKeys = overArg(Object.keys, Object),
		        nativeMax = Math.max,
		        nativeMin = Math.min,
		        nativeNow = Date.now,
		        nativeParseInt = context.parseInt,
		        nativeRandom = Math.random,
		        nativeReverse = arrayProto.reverse;

		    /* Built-in method references that are verified to be native. */
		    var DataView = getNative(context, 'DataView'),
		        Map = getNative(context, 'Map'),
		        Promise = getNative(context, 'Promise'),
		        Set = getNative(context, 'Set'),
		        WeakMap = getNative(context, 'WeakMap'),
		        nativeCreate = getNative(Object, 'create');

		    /** Used to store function metadata. */
		    var metaMap = WeakMap && new WeakMap;

		    /** Used to lookup unminified function names. */
		    var realNames = {};

		    /** Used to detect maps, sets, and weakmaps. */
		    var dataViewCtorString = toSource(DataView),
		        mapCtorString = toSource(Map),
		        promiseCtorString = toSource(Promise),
		        setCtorString = toSource(Set),
		        weakMapCtorString = toSource(WeakMap);

		    /** Used to convert symbols to primitives and strings. */
		    var symbolProto = Symbol ? Symbol.prototype : undefined$1,
		        symbolValueOf = symbolProto ? symbolProto.valueOf : undefined$1,
		        symbolToString = symbolProto ? symbolProto.toString : undefined$1;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates a `lodash` object which wraps `value` to enable implicit method
		     * chain sequences. Methods that operate on and return arrays, collections,
		     * and functions can be chained together. Methods that retrieve a single value
		     * or may return a primitive value will automatically end the chain sequence
		     * and return the unwrapped value. Otherwise, the value must be unwrapped
		     * with `_#value`.
		     *
		     * Explicit chain sequences, which must be unwrapped with `_#value`, may be
		     * enabled using `_.chain`.
		     *
		     * The execution of chained methods is lazy, that is, it's deferred until
		     * `_#value` is implicitly or explicitly called.
		     *
		     * Lazy evaluation allows several methods to support shortcut fusion.
		     * Shortcut fusion is an optimization to merge iteratee calls; this avoids
		     * the creation of intermediate arrays and can greatly reduce the number of
		     * iteratee executions. Sections of a chain sequence qualify for shortcut
		     * fusion if the section is applied to an array and iteratees accept only
		     * one argument. The heuristic for whether a section qualifies for shortcut
		     * fusion is subject to change.
		     *
		     * Chaining is supported in custom builds as long as the `_#value` method is
		     * directly or indirectly included in the build.
		     *
		     * In addition to lodash methods, wrappers have `Array` and `String` methods.
		     *
		     * The wrapper `Array` methods are:
		     * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
		     *
		     * The wrapper `String` methods are:
		     * `replace` and `split`
		     *
		     * The wrapper methods that support shortcut fusion are:
		     * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
		     * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
		     * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
		     *
		     * The chainable wrapper methods are:
		     * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
		     * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
		     * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
		     * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
		     * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
		     * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
		     * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
		     * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
		     * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
		     * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
		     * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
		     * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
		     * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
		     * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
		     * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
		     * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
		     * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
		     * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
		     * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
		     * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
		     * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
		     * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
		     * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
		     * `zipObject`, `zipObjectDeep`, and `zipWith`
		     *
		     * The wrapper methods that are **not** chainable by default are:
		     * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
		     * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
		     * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
		     * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
		     * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
		     * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
		     * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
		     * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
		     * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
		     * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
		     * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
		     * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
		     * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
		     * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
		     * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
		     * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
		     * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
		     * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
		     * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
		     * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
		     * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
		     * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
		     * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
		     * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
		     * `upperFirst`, `value`, and `words`
		     *
		     * @name _
		     * @constructor
		     * @category Seq
		     * @param {*} value The value to wrap in a `lodash` instance.
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * function square(n) {
		     *   return n * n;
		     * }
		     *
		     * var wrapped = _([1, 2, 3]);
		     *
		     * // Returns an unwrapped value.
		     * wrapped.reduce(_.add);
		     * // => 6
		     *
		     * // Returns a wrapped value.
		     * var squares = wrapped.map(square);
		     *
		     * _.isArray(squares);
		     * // => false
		     *
		     * _.isArray(squares.value());
		     * // => true
		     */
		    function lodash(value) {
		      if (isObjectLike(value) && !isArray(value) && !(value instanceof LazyWrapper)) {
		        if (value instanceof LodashWrapper) {
		          return value;
		        }
		        if (hasOwnProperty.call(value, '__wrapped__')) {
		          return wrapperClone(value);
		        }
		      }
		      return new LodashWrapper(value);
		    }

		    /**
		     * The base implementation of `_.create` without support for assigning
		     * properties to the created object.
		     *
		     * @private
		     * @param {Object} proto The object to inherit from.
		     * @returns {Object} Returns the new object.
		     */
		    var baseCreate = (function() {
		      function object() {}
		      return function(proto) {
		        if (!isObject(proto)) {
		          return {};
		        }
		        if (objectCreate) {
		          return objectCreate(proto);
		        }
		        object.prototype = proto;
		        var result = new object;
		        object.prototype = undefined$1;
		        return result;
		      };
		    }());

		    /**
		     * The function whose prototype chain sequence wrappers inherit from.
		     *
		     * @private
		     */
		    function baseLodash() {
		      // No operation performed.
		    }

		    /**
		     * The base constructor for creating `lodash` wrapper objects.
		     *
		     * @private
		     * @param {*} value The value to wrap.
		     * @param {boolean} [chainAll] Enable explicit method chain sequences.
		     */
		    function LodashWrapper(value, chainAll) {
		      this.__wrapped__ = value;
		      this.__actions__ = [];
		      this.__chain__ = !!chainAll;
		      this.__index__ = 0;
		      this.__values__ = undefined$1;
		    }

		    /**
		     * By default, the template delimiters used by lodash are like those in
		     * embedded Ruby (ERB) as well as ES2015 template strings. Change the
		     * following template settings to use alternative delimiters.
		     *
		     * **Security:** See
		     * [threat model](https://github.com/lodash/lodash/blob/main/threat-model.md)
		     * — `_.template` is insecure and will be removed in v5.
		     *
		     * @static
		     * @memberOf _
		     * @type {Object}
		     */
		    lodash.templateSettings = {

		      /**
		       * Used to detect `data` property values to be HTML-escaped.
		       *
		       * @memberOf _.templateSettings
		       * @type {RegExp}
		       */
		      'escape': reEscape,

		      /**
		       * Used to detect code to be evaluated.
		       *
		       * @memberOf _.templateSettings
		       * @type {RegExp}
		       */
		      'evaluate': reEvaluate,

		      /**
		       * Used to detect `data` property values to inject.
		       *
		       * @memberOf _.templateSettings
		       * @type {RegExp}
		       */
		      'interpolate': reInterpolate,

		      /**
		       * Used to reference the data object in the template text.
		       *
		       * @memberOf _.templateSettings
		       * @type {string}
		       */
		      'variable': '',

		      /**
		       * Used to import variables into the compiled template.
		       *
		       * @memberOf _.templateSettings
		       * @type {Object}
		       */
		      'imports': {

		        /**
		         * A reference to the `lodash` function.
		         *
		         * @memberOf _.templateSettings.imports
		         * @type {Function}
		         */
		        '_': lodash
		      }
		    };

		    // Ensure wrappers are instances of `baseLodash`.
		    lodash.prototype = baseLodash.prototype;
		    lodash.prototype.constructor = lodash;

		    LodashWrapper.prototype = baseCreate(baseLodash.prototype);
		    LodashWrapper.prototype.constructor = LodashWrapper;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
		     *
		     * @private
		     * @constructor
		     * @param {*} value The value to wrap.
		     */
		    function LazyWrapper(value) {
		      this.__wrapped__ = value;
		      this.__actions__ = [];
		      this.__dir__ = 1;
		      this.__filtered__ = false;
		      this.__iteratees__ = [];
		      this.__takeCount__ = MAX_ARRAY_LENGTH;
		      this.__views__ = [];
		    }

		    /**
		     * Creates a clone of the lazy wrapper object.
		     *
		     * @private
		     * @name clone
		     * @memberOf LazyWrapper
		     * @returns {Object} Returns the cloned `LazyWrapper` object.
		     */
		    function lazyClone() {
		      var result = new LazyWrapper(this.__wrapped__);
		      result.__actions__ = copyArray(this.__actions__);
		      result.__dir__ = this.__dir__;
		      result.__filtered__ = this.__filtered__;
		      result.__iteratees__ = copyArray(this.__iteratees__);
		      result.__takeCount__ = this.__takeCount__;
		      result.__views__ = copyArray(this.__views__);
		      return result;
		    }

		    /**
		     * Reverses the direction of lazy iteration.
		     *
		     * @private
		     * @name reverse
		     * @memberOf LazyWrapper
		     * @returns {Object} Returns the new reversed `LazyWrapper` object.
		     */
		    function lazyReverse() {
		      if (this.__filtered__) {
		        var result = new LazyWrapper(this);
		        result.__dir__ = -1;
		        result.__filtered__ = true;
		      } else {
		        result = this.clone();
		        result.__dir__ *= -1;
		      }
		      return result;
		    }

		    /**
		     * Extracts the unwrapped value from its lazy wrapper.
		     *
		     * @private
		     * @name value
		     * @memberOf LazyWrapper
		     * @returns {*} Returns the unwrapped value.
		     */
		    function lazyValue() {
		      var array = this.__wrapped__.value(),
		          dir = this.__dir__,
		          isArr = isArray(array),
		          isRight = dir < 0,
		          arrLength = isArr ? array.length : 0,
		          view = getView(0, arrLength, this.__views__),
		          start = view.start,
		          end = view.end,
		          length = end - start,
		          index = isRight ? end : (start - 1),
		          iteratees = this.__iteratees__,
		          iterLength = iteratees.length,
		          resIndex = 0,
		          takeCount = nativeMin(length, this.__takeCount__);

		      if (!isArr || (!isRight && arrLength == length && takeCount == length)) {
		        return baseWrapperValue(array, this.__actions__);
		      }
		      var result = [];

		      outer:
		      while (length-- && resIndex < takeCount) {
		        index += dir;

		        var iterIndex = -1,
		            value = array[index];

		        while (++iterIndex < iterLength) {
		          var data = iteratees[iterIndex],
		              iteratee = data.iteratee,
		              type = data.type,
		              computed = iteratee(value);

		          if (type == LAZY_MAP_FLAG) {
		            value = computed;
		          } else if (!computed) {
		            if (type == LAZY_FILTER_FLAG) {
		              continue outer;
		            } else {
		              break outer;
		            }
		          }
		        }
		        result[resIndex++] = value;
		      }
		      return result;
		    }

		    // Ensure `LazyWrapper` is an instance of `baseLodash`.
		    LazyWrapper.prototype = baseCreate(baseLodash.prototype);
		    LazyWrapper.prototype.constructor = LazyWrapper;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates a hash object.
		     *
		     * @private
		     * @constructor
		     * @param {Array} [entries] The key-value pairs to cache.
		     */
		    function Hash(entries) {
		      var index = -1,
		          length = entries == null ? 0 : entries.length;

		      this.clear();
		      while (++index < length) {
		        var entry = entries[index];
		        this.set(entry[0], entry[1]);
		      }
		    }

		    /**
		     * Removes all key-value entries from the hash.
		     *
		     * @private
		     * @name clear
		     * @memberOf Hash
		     */
		    function hashClear() {
		      this.__data__ = nativeCreate ? nativeCreate(null) : {};
		      this.size = 0;
		    }

		    /**
		     * Removes `key` and its value from the hash.
		     *
		     * @private
		     * @name delete
		     * @memberOf Hash
		     * @param {Object} hash The hash to modify.
		     * @param {string} key The key of the value to remove.
		     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
		     */
		    function hashDelete(key) {
		      var result = this.has(key) && delete this.__data__[key];
		      this.size -= result ? 1 : 0;
		      return result;
		    }

		    /**
		     * Gets the hash value for `key`.
		     *
		     * @private
		     * @name get
		     * @memberOf Hash
		     * @param {string} key The key of the value to get.
		     * @returns {*} Returns the entry value.
		     */
		    function hashGet(key) {
		      var data = this.__data__;
		      if (nativeCreate) {
		        var result = data[key];
		        return result === HASH_UNDEFINED ? undefined$1 : result;
		      }
		      return hasOwnProperty.call(data, key) ? data[key] : undefined$1;
		    }

		    /**
		     * Checks if a hash value for `key` exists.
		     *
		     * @private
		     * @name has
		     * @memberOf Hash
		     * @param {string} key The key of the entry to check.
		     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
		     */
		    function hashHas(key) {
		      var data = this.__data__;
		      return nativeCreate ? (data[key] !== undefined$1) : hasOwnProperty.call(data, key);
		    }

		    /**
		     * Sets the hash `key` to `value`.
		     *
		     * @private
		     * @name set
		     * @memberOf Hash
		     * @param {string} key The key of the value to set.
		     * @param {*} value The value to set.
		     * @returns {Object} Returns the hash instance.
		     */
		    function hashSet(key, value) {
		      var data = this.__data__;
		      this.size += this.has(key) ? 0 : 1;
		      data[key] = (nativeCreate && value === undefined$1) ? HASH_UNDEFINED : value;
		      return this;
		    }

		    // Add methods to `Hash`.
		    Hash.prototype.clear = hashClear;
		    Hash.prototype['delete'] = hashDelete;
		    Hash.prototype.get = hashGet;
		    Hash.prototype.has = hashHas;
		    Hash.prototype.set = hashSet;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates an list cache object.
		     *
		     * @private
		     * @constructor
		     * @param {Array} [entries] The key-value pairs to cache.
		     */
		    function ListCache(entries) {
		      var index = -1,
		          length = entries == null ? 0 : entries.length;

		      this.clear();
		      while (++index < length) {
		        var entry = entries[index];
		        this.set(entry[0], entry[1]);
		      }
		    }

		    /**
		     * Removes all key-value entries from the list cache.
		     *
		     * @private
		     * @name clear
		     * @memberOf ListCache
		     */
		    function listCacheClear() {
		      this.__data__ = [];
		      this.size = 0;
		    }

		    /**
		     * Removes `key` and its value from the list cache.
		     *
		     * @private
		     * @name delete
		     * @memberOf ListCache
		     * @param {string} key The key of the value to remove.
		     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
		     */
		    function listCacheDelete(key) {
		      var data = this.__data__,
		          index = assocIndexOf(data, key);

		      if (index < 0) {
		        return false;
		      }
		      var lastIndex = data.length - 1;
		      if (index == lastIndex) {
		        data.pop();
		      } else {
		        splice.call(data, index, 1);
		      }
		      --this.size;
		      return true;
		    }

		    /**
		     * Gets the list cache value for `key`.
		     *
		     * @private
		     * @name get
		     * @memberOf ListCache
		     * @param {string} key The key of the value to get.
		     * @returns {*} Returns the entry value.
		     */
		    function listCacheGet(key) {
		      var data = this.__data__,
		          index = assocIndexOf(data, key);

		      return index < 0 ? undefined$1 : data[index][1];
		    }

		    /**
		     * Checks if a list cache value for `key` exists.
		     *
		     * @private
		     * @name has
		     * @memberOf ListCache
		     * @param {string} key The key of the entry to check.
		     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
		     */
		    function listCacheHas(key) {
		      return assocIndexOf(this.__data__, key) > -1;
		    }

		    /**
		     * Sets the list cache `key` to `value`.
		     *
		     * @private
		     * @name set
		     * @memberOf ListCache
		     * @param {string} key The key of the value to set.
		     * @param {*} value The value to set.
		     * @returns {Object} Returns the list cache instance.
		     */
		    function listCacheSet(key, value) {
		      var data = this.__data__,
		          index = assocIndexOf(data, key);

		      if (index < 0) {
		        ++this.size;
		        data.push([key, value]);
		      } else {
		        data[index][1] = value;
		      }
		      return this;
		    }

		    // Add methods to `ListCache`.
		    ListCache.prototype.clear = listCacheClear;
		    ListCache.prototype['delete'] = listCacheDelete;
		    ListCache.prototype.get = listCacheGet;
		    ListCache.prototype.has = listCacheHas;
		    ListCache.prototype.set = listCacheSet;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates a map cache object to store key-value pairs.
		     *
		     * @private
		     * @constructor
		     * @param {Array} [entries] The key-value pairs to cache.
		     */
		    function MapCache(entries) {
		      var index = -1,
		          length = entries == null ? 0 : entries.length;

		      this.clear();
		      while (++index < length) {
		        var entry = entries[index];
		        this.set(entry[0], entry[1]);
		      }
		    }

		    /**
		     * Removes all key-value entries from the map.
		     *
		     * @private
		     * @name clear
		     * @memberOf MapCache
		     */
		    function mapCacheClear() {
		      this.size = 0;
		      this.__data__ = {
		        'hash': new Hash,
		        'map': new (Map || ListCache),
		        'string': new Hash
		      };
		    }

		    /**
		     * Removes `key` and its value from the map.
		     *
		     * @private
		     * @name delete
		     * @memberOf MapCache
		     * @param {string} key The key of the value to remove.
		     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
		     */
		    function mapCacheDelete(key) {
		      var result = getMapData(this, key)['delete'](key);
		      this.size -= result ? 1 : 0;
		      return result;
		    }

		    /**
		     * Gets the map value for `key`.
		     *
		     * @private
		     * @name get
		     * @memberOf MapCache
		     * @param {string} key The key of the value to get.
		     * @returns {*} Returns the entry value.
		     */
		    function mapCacheGet(key) {
		      return getMapData(this, key).get(key);
		    }

		    /**
		     * Checks if a map value for `key` exists.
		     *
		     * @private
		     * @name has
		     * @memberOf MapCache
		     * @param {string} key The key of the entry to check.
		     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
		     */
		    function mapCacheHas(key) {
		      return getMapData(this, key).has(key);
		    }

		    /**
		     * Sets the map `key` to `value`.
		     *
		     * @private
		     * @name set
		     * @memberOf MapCache
		     * @param {string} key The key of the value to set.
		     * @param {*} value The value to set.
		     * @returns {Object} Returns the map cache instance.
		     */
		    function mapCacheSet(key, value) {
		      var data = getMapData(this, key),
		          size = data.size;

		      data.set(key, value);
		      this.size += data.size == size ? 0 : 1;
		      return this;
		    }

		    // Add methods to `MapCache`.
		    MapCache.prototype.clear = mapCacheClear;
		    MapCache.prototype['delete'] = mapCacheDelete;
		    MapCache.prototype.get = mapCacheGet;
		    MapCache.prototype.has = mapCacheHas;
		    MapCache.prototype.set = mapCacheSet;

		    /*------------------------------------------------------------------------*/

		    /**
		     *
		     * Creates an array cache object to store unique values.
		     *
		     * @private
		     * @constructor
		     * @param {Array} [values] The values to cache.
		     */
		    function SetCache(values) {
		      var index = -1,
		          length = values == null ? 0 : values.length;

		      this.__data__ = new MapCache;
		      while (++index < length) {
		        this.add(values[index]);
		      }
		    }

		    /**
		     * Adds `value` to the array cache.
		     *
		     * @private
		     * @name add
		     * @memberOf SetCache
		     * @alias push
		     * @param {*} value The value to cache.
		     * @returns {Object} Returns the cache instance.
		     */
		    function setCacheAdd(value) {
		      this.__data__.set(value, HASH_UNDEFINED);
		      return this;
		    }

		    /**
		     * Checks if `value` is in the array cache.
		     *
		     * @private
		     * @name has
		     * @memberOf SetCache
		     * @param {*} value The value to search for.
		     * @returns {boolean} Returns `true` if `value` is found, else `false`.
		     */
		    function setCacheHas(value) {
		      return this.__data__.has(value);
		    }

		    // Add methods to `SetCache`.
		    SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
		    SetCache.prototype.has = setCacheHas;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates a stack cache object to store key-value pairs.
		     *
		     * @private
		     * @constructor
		     * @param {Array} [entries] The key-value pairs to cache.
		     */
		    function Stack(entries) {
		      var data = this.__data__ = new ListCache(entries);
		      this.size = data.size;
		    }

		    /**
		     * Removes all key-value entries from the stack.
		     *
		     * @private
		     * @name clear
		     * @memberOf Stack
		     */
		    function stackClear() {
		      this.__data__ = new ListCache;
		      this.size = 0;
		    }

		    /**
		     * Removes `key` and its value from the stack.
		     *
		     * @private
		     * @name delete
		     * @memberOf Stack
		     * @param {string} key The key of the value to remove.
		     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
		     */
		    function stackDelete(key) {
		      var data = this.__data__,
		          result = data['delete'](key);

		      this.size = data.size;
		      return result;
		    }

		    /**
		     * Gets the stack value for `key`.
		     *
		     * @private
		     * @name get
		     * @memberOf Stack
		     * @param {string} key The key of the value to get.
		     * @returns {*} Returns the entry value.
		     */
		    function stackGet(key) {
		      return this.__data__.get(key);
		    }

		    /**
		     * Checks if a stack value for `key` exists.
		     *
		     * @private
		     * @name has
		     * @memberOf Stack
		     * @param {string} key The key of the entry to check.
		     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
		     */
		    function stackHas(key) {
		      return this.__data__.has(key);
		    }

		    /**
		     * Sets the stack `key` to `value`.
		     *
		     * @private
		     * @name set
		     * @memberOf Stack
		     * @param {string} key The key of the value to set.
		     * @param {*} value The value to set.
		     * @returns {Object} Returns the stack cache instance.
		     */
		    function stackSet(key, value) {
		      var data = this.__data__;
		      if (data instanceof ListCache) {
		        var pairs = data.__data__;
		        if (!Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
		          pairs.push([key, value]);
		          this.size = ++data.size;
		          return this;
		        }
		        data = this.__data__ = new MapCache(pairs);
		      }
		      data.set(key, value);
		      this.size = data.size;
		      return this;
		    }

		    // Add methods to `Stack`.
		    Stack.prototype.clear = stackClear;
		    Stack.prototype['delete'] = stackDelete;
		    Stack.prototype.get = stackGet;
		    Stack.prototype.has = stackHas;
		    Stack.prototype.set = stackSet;

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates an array of the enumerable property names of the array-like `value`.
		     *
		     * @private
		     * @param {*} value The value to query.
		     * @param {boolean} inherited Specify returning inherited property names.
		     * @returns {Array} Returns the array of property names.
		     */
		    function arrayLikeKeys(value, inherited) {
		      var isArr = isArray(value),
		          isArg = !isArr && isArguments(value),
		          isBuff = !isArr && !isArg && isBuffer(value),
		          isType = !isArr && !isArg && !isBuff && isTypedArray(value),
		          skipIndexes = isArr || isArg || isBuff || isType,
		          result = skipIndexes ? baseTimes(value.length, String) : [],
		          length = result.length;

		      for (var key in value) {
		        if ((inherited || hasOwnProperty.call(value, key)) &&
		            !(skipIndexes && (
		               // Safari 9 has enumerable `arguments.length` in strict mode.
		               key == 'length' ||
		               // Node.js 0.10 has enumerable non-index properties on buffers.
		               (isBuff && (key == 'offset' || key == 'parent')) ||
		               // PhantomJS 2 has enumerable non-index properties on typed arrays.
		               (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
		               // Skip index properties.
		               isIndex(key, length)
		            ))) {
		          result.push(key);
		        }
		      }
		      return result;
		    }

		    /**
		     * A specialized version of `_.sample` for arrays.
		     *
		     * @private
		     * @param {Array} array The array to sample.
		     * @returns {*} Returns the random element.
		     */
		    function arraySample(array) {
		      var length = array.length;
		      return length ? array[baseRandom(0, length - 1)] : undefined$1;
		    }

		    /**
		     * A specialized version of `_.sampleSize` for arrays.
		     *
		     * @private
		     * @param {Array} array The array to sample.
		     * @param {number} n The number of elements to sample.
		     * @returns {Array} Returns the random elements.
		     */
		    function arraySampleSize(array, n) {
		      return shuffleSelf(copyArray(array), baseClamp(n, 0, array.length));
		    }

		    /**
		     * A specialized version of `_.shuffle` for arrays.
		     *
		     * @private
		     * @param {Array} array The array to shuffle.
		     * @returns {Array} Returns the new shuffled array.
		     */
		    function arrayShuffle(array) {
		      return shuffleSelf(copyArray(array));
		    }

		    /**
		     * This function is like `assignValue` except that it doesn't assign
		     * `undefined` values.
		     *
		     * @private
		     * @param {Object} object The object to modify.
		     * @param {string} key The key of the property to assign.
		     * @param {*} value The value to assign.
		     */
		    function assignMergeValue(object, key, value) {
		      if ((value !== undefined$1 && !eq(object[key], value)) ||
		          (value === undefined$1 && !(key in object))) {
		        baseAssignValue(object, key, value);
		      }
		    }

		    /**
		     * Assigns `value` to `key` of `object` if the existing value is not equivalent
		     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons.
		     *
		     * @private
		     * @param {Object} object The object to modify.
		     * @param {string} key The key of the property to assign.
		     * @param {*} value The value to assign.
		     */
		    function assignValue(object, key, value) {
		      var objValue = object[key];
		      if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
		          (value === undefined$1 && !(key in object))) {
		        baseAssignValue(object, key, value);
		      }
		    }

		    /**
		     * Gets the index at which the `key` is found in `array` of key-value pairs.
		     *
		     * @private
		     * @param {Array} array The array to inspect.
		     * @param {*} key The key to search for.
		     * @returns {number} Returns the index of the matched value, else `-1`.
		     */
		    function assocIndexOf(array, key) {
		      var length = array.length;
		      while (length--) {
		        if (eq(array[length][0], key)) {
		          return length;
		        }
		      }
		      return -1;
		    }

		    /**
		     * Aggregates elements of `collection` on `accumulator` with keys transformed
		     * by `iteratee` and values set by `setter`.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} setter The function to set `accumulator` values.
		     * @param {Function} iteratee The iteratee to transform keys.
		     * @param {Object} accumulator The initial aggregated object.
		     * @returns {Function} Returns `accumulator`.
		     */
		    function baseAggregator(collection, setter, iteratee, accumulator) {
		      baseEach(collection, function(value, key, collection) {
		        setter(accumulator, value, iteratee(value), collection);
		      });
		      return accumulator;
		    }

		    /**
		     * The base implementation of `_.assign` without support for multiple sources
		     * or `customizer` functions.
		     *
		     * @private
		     * @param {Object} object The destination object.
		     * @param {Object} source The source object.
		     * @returns {Object} Returns `object`.
		     */
		    function baseAssign(object, source) {
		      return object && copyObject(source, keys(source), object);
		    }

		    /**
		     * The base implementation of `_.assignIn` without support for multiple sources
		     * or `customizer` functions.
		     *
		     * @private
		     * @param {Object} object The destination object.
		     * @param {Object} source The source object.
		     * @returns {Object} Returns `object`.
		     */
		    function baseAssignIn(object, source) {
		      return object && copyObject(source, keysIn(source), object);
		    }

		    /**
		     * The base implementation of `assignValue` and `assignMergeValue` without
		     * value checks.
		     *
		     * @private
		     * @param {Object} object The object to modify.
		     * @param {string} key The key of the property to assign.
		     * @param {*} value The value to assign.
		     */
		    function baseAssignValue(object, key, value) {
		      if (key == '__proto__' && defineProperty) {
		        defineProperty(object, key, {
		          'configurable': true,
		          'enumerable': true,
		          'value': value,
		          'writable': true
		        });
		      } else {
		        object[key] = value;
		      }
		    }

		    /**
		     * The base implementation of `_.at` without support for individual paths.
		     *
		     * @private
		     * @param {Object} object The object to iterate over.
		     * @param {string[]} paths The property paths to pick.
		     * @returns {Array} Returns the picked elements.
		     */
		    function baseAt(object, paths) {
		      var index = -1,
		          length = paths.length,
		          result = Array(length),
		          skip = object == null;

		      while (++index < length) {
		        result[index] = skip ? undefined$1 : get(object, paths[index]);
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.clamp` which doesn't coerce arguments.
		     *
		     * @private
		     * @param {number} number The number to clamp.
		     * @param {number} [lower] The lower bound.
		     * @param {number} upper The upper bound.
		     * @returns {number} Returns the clamped number.
		     */
		    function baseClamp(number, lower, upper) {
		      if (number === number) {
		        if (upper !== undefined$1) {
		          number = number <= upper ? number : upper;
		        }
		        if (lower !== undefined$1) {
		          number = number >= lower ? number : lower;
		        }
		      }
		      return number;
		    }

		    /**
		     * The base implementation of `_.clone` and `_.cloneDeep` which tracks
		     * traversed objects.
		     *
		     * @private
		     * @param {*} value The value to clone.
		     * @param {boolean} bitmask The bitmask flags.
		     *  1 - Deep clone
		     *  2 - Flatten inherited properties
		     *  4 - Clone symbols
		     * @param {Function} [customizer] The function to customize cloning.
		     * @param {string} [key] The key of `value`.
		     * @param {Object} [object] The parent object of `value`.
		     * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
		     * @returns {*} Returns the cloned value.
		     */
		    function baseClone(value, bitmask, customizer, key, object, stack) {
		      var result,
		          isDeep = bitmask & CLONE_DEEP_FLAG,
		          isFlat = bitmask & CLONE_FLAT_FLAG,
		          isFull = bitmask & CLONE_SYMBOLS_FLAG;

		      if (customizer) {
		        result = object ? customizer(value, key, object, stack) : customizer(value);
		      }
		      if (result !== undefined$1) {
		        return result;
		      }
		      if (!isObject(value)) {
		        return value;
		      }
		      var isArr = isArray(value);
		      if (isArr) {
		        result = initCloneArray(value);
		        if (!isDeep) {
		          return copyArray(value, result);
		        }
		      } else {
		        var tag = getTag(value),
		            isFunc = tag == funcTag || tag == genTag;

		        if (isBuffer(value)) {
		          return cloneBuffer(value, isDeep);
		        }
		        if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
		          result = (isFlat || isFunc) ? {} : initCloneObject(value);
		          if (!isDeep) {
		            return isFlat
		              ? copySymbolsIn(value, baseAssignIn(result, value))
		              : copySymbols(value, baseAssign(result, value));
		          }
		        } else {
		          if (!cloneableTags[tag]) {
		            return object ? value : {};
		          }
		          result = initCloneByTag(value, tag, isDeep);
		        }
		      }
		      // Check for circular references and return its corresponding clone.
		      stack || (stack = new Stack);
		      var stacked = stack.get(value);
		      if (stacked) {
		        return stacked;
		      }
		      stack.set(value, result);

		      if (isSet(value)) {
		        value.forEach(function(subValue) {
		          result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
		        });
		      } else if (isMap(value)) {
		        value.forEach(function(subValue, key) {
		          result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
		        });
		      }

		      var keysFunc = isFull
		        ? (isFlat ? getAllKeysIn : getAllKeys)
		        : (isFlat ? keysIn : keys);

		      var props = isArr ? undefined$1 : keysFunc(value);
		      arrayEach(props || value, function(subValue, key) {
		        if (props) {
		          key = subValue;
		          subValue = value[key];
		        }
		        // Recursively populate clone (susceptible to call stack limits).
		        assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
		      });
		      return result;
		    }

		    /**
		     * The base implementation of `_.conforms` which doesn't clone `source`.
		     *
		     * @private
		     * @param {Object} source The object of property predicates to conform to.
		     * @returns {Function} Returns the new spec function.
		     */
		    function baseConforms(source) {
		      var props = keys(source);
		      return function(object) {
		        return baseConformsTo(object, source, props);
		      };
		    }

		    /**
		     * The base implementation of `_.conformsTo` which accepts `props` to check.
		     *
		     * @private
		     * @param {Object} object The object to inspect.
		     * @param {Object} source The object of property predicates to conform to.
		     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
		     */
		    function baseConformsTo(object, source, props) {
		      var length = props.length;
		      if (object == null) {
		        return !length;
		      }
		      object = Object(object);
		      while (length--) {
		        var key = props[length],
		            predicate = source[key],
		            value = object[key];

		        if ((value === undefined$1 && !(key in object)) || !predicate(value)) {
		          return false;
		        }
		      }
		      return true;
		    }

		    /**
		     * The base implementation of `_.delay` and `_.defer` which accepts `args`
		     * to provide to `func`.
		     *
		     * @private
		     * @param {Function} func The function to delay.
		     * @param {number} wait The number of milliseconds to delay invocation.
		     * @param {Array} args The arguments to provide to `func`.
		     * @returns {number|Object} Returns the timer id or timeout object.
		     */
		    function baseDelay(func, wait, args) {
		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      return setTimeout(function() { func.apply(undefined$1, args); }, wait);
		    }

		    /**
		     * The base implementation of methods like `_.difference` without support
		     * for excluding multiple arrays or iteratee shorthands.
		     *
		     * @private
		     * @param {Array} array The array to inspect.
		     * @param {Array} values The values to exclude.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of filtered values.
		     */
		    function baseDifference(array, values, iteratee, comparator) {
		      var index = -1,
		          includes = arrayIncludes,
		          isCommon = true,
		          length = array.length,
		          result = [],
		          valuesLength = values.length;

		      if (!length) {
		        return result;
		      }
		      if (iteratee) {
		        values = arrayMap(values, baseUnary(iteratee));
		      }
		      if (comparator) {
		        includes = arrayIncludesWith;
		        isCommon = false;
		      }
		      else if (values.length >= LARGE_ARRAY_SIZE) {
		        includes = cacheHas;
		        isCommon = false;
		        values = new SetCache(values);
		      }
		      outer:
		      while (++index < length) {
		        var value = array[index],
		            computed = iteratee == null ? value : iteratee(value);

		        value = (comparator || value !== 0) ? value : 0;
		        if (isCommon && computed === computed) {
		          var valuesIndex = valuesLength;
		          while (valuesIndex--) {
		            if (values[valuesIndex] === computed) {
		              continue outer;
		            }
		          }
		          result.push(value);
		        }
		        else if (!includes(values, computed, comparator)) {
		          result.push(value);
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.forEach` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @returns {Array|Object} Returns `collection`.
		     */
		    var baseEach = createBaseEach(baseForOwn);

		    /**
		     * The base implementation of `_.forEachRight` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @returns {Array|Object} Returns `collection`.
		     */
		    var baseEachRight = createBaseEach(baseForOwnRight, true);

		    /**
		     * The base implementation of `_.every` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} predicate The function invoked per iteration.
		     * @returns {boolean} Returns `true` if all elements pass the predicate check,
		     *  else `false`
		     */
		    function baseEvery(collection, predicate) {
		      var result = true;
		      baseEach(collection, function(value, index, collection) {
		        result = !!predicate(value, index, collection);
		        return result;
		      });
		      return result;
		    }

		    /**
		     * The base implementation of methods like `_.max` and `_.min` which accepts a
		     * `comparator` to determine the extremum value.
		     *
		     * @private
		     * @param {Array} array The array to iterate over.
		     * @param {Function} iteratee The iteratee invoked per iteration.
		     * @param {Function} comparator The comparator used to compare values.
		     * @returns {*} Returns the extremum value.
		     */
		    function baseExtremum(array, iteratee, comparator) {
		      var index = -1,
		          length = array.length;

		      while (++index < length) {
		        var value = array[index],
		            current = iteratee(value);

		        if (current != null && (computed === undefined$1
		              ? (current === current && !isSymbol(current))
		              : comparator(current, computed)
		            )) {
		          var computed = current,
		              result = value;
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.fill` without an iteratee call guard.
		     *
		     * @private
		     * @param {Array} array The array to fill.
		     * @param {*} value The value to fill `array` with.
		     * @param {number} [start=0] The start position.
		     * @param {number} [end=array.length] The end position.
		     * @returns {Array} Returns `array`.
		     */
		    function baseFill(array, value, start, end) {
		      var length = array.length;

		      start = toInteger(start);
		      if (start < 0) {
		        start = -start > length ? 0 : (length + start);
		      }
		      end = (end === undefined$1 || end > length) ? length : toInteger(end);
		      if (end < 0) {
		        end += length;
		      }
		      end = start > end ? 0 : toLength(end);
		      while (start < end) {
		        array[start++] = value;
		      }
		      return array;
		    }

		    /**
		     * The base implementation of `_.filter` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} predicate The function invoked per iteration.
		     * @returns {Array} Returns the new filtered array.
		     */
		    function baseFilter(collection, predicate) {
		      var result = [];
		      baseEach(collection, function(value, index, collection) {
		        if (predicate(value, index, collection)) {
		          result.push(value);
		        }
		      });
		      return result;
		    }

		    /**
		     * The base implementation of `_.flatten` with support for restricting flattening.
		     *
		     * @private
		     * @param {Array} array The array to flatten.
		     * @param {number} depth The maximum recursion depth.
		     * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
		     * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
		     * @param {Array} [result=[]] The initial result value.
		     * @returns {Array} Returns the new flattened array.
		     */
		    function baseFlatten(array, depth, predicate, isStrict, result) {
		      var index = -1,
		          length = array.length;

		      predicate || (predicate = isFlattenable);
		      result || (result = []);

		      while (++index < length) {
		        var value = array[index];
		        if (depth > 0 && predicate(value)) {
		          if (depth > 1) {
		            // Recursively flatten arrays (susceptible to call stack limits).
		            baseFlatten(value, depth - 1, predicate, isStrict, result);
		          } else {
		            arrayPush(result, value);
		          }
		        } else if (!isStrict) {
		          result[result.length] = value;
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `baseForOwn` which iterates over `object`
		     * properties returned by `keysFunc` and invokes `iteratee` for each property.
		     * Iteratee functions may exit iteration early by explicitly returning `false`.
		     *
		     * @private
		     * @param {Object} object The object to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @param {Function} keysFunc The function to get the keys of `object`.
		     * @returns {Object} Returns `object`.
		     */
		    var baseFor = createBaseFor();

		    /**
		     * This function is like `baseFor` except that it iterates over properties
		     * in the opposite order.
		     *
		     * @private
		     * @param {Object} object The object to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @param {Function} keysFunc The function to get the keys of `object`.
		     * @returns {Object} Returns `object`.
		     */
		    var baseForRight = createBaseFor(true);

		    /**
		     * The base implementation of `_.forOwn` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Object} object The object to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @returns {Object} Returns `object`.
		     */
		    function baseForOwn(object, iteratee) {
		      return object && baseFor(object, iteratee, keys);
		    }

		    /**
		     * The base implementation of `_.forOwnRight` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Object} object The object to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @returns {Object} Returns `object`.
		     */
		    function baseForOwnRight(object, iteratee) {
		      return object && baseForRight(object, iteratee, keys);
		    }

		    /**
		     * The base implementation of `_.functions` which creates an array of
		     * `object` function property names filtered from `props`.
		     *
		     * @private
		     * @param {Object} object The object to inspect.
		     * @param {Array} props The property names to filter.
		     * @returns {Array} Returns the function names.
		     */
		    function baseFunctions(object, props) {
		      return arrayFilter(props, function(key) {
		        return isFunction(object[key]);
		      });
		    }

		    /**
		     * The base implementation of `_.get` without support for default values.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path of the property to get.
		     * @returns {*} Returns the resolved value.
		     */
		    function baseGet(object, path) {
		      path = castPath(path, object);

		      var index = 0,
		          length = path.length;

		      while (object != null && index < length) {
		        object = object[toKey(path[index++])];
		      }
		      return (index && index == length) ? object : undefined$1;
		    }

		    /**
		     * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
		     * `keysFunc` and `symbolsFunc` to get the enumerable property names and
		     * symbols of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {Function} keysFunc The function to get the keys of `object`.
		     * @param {Function} symbolsFunc The function to get the symbols of `object`.
		     * @returns {Array} Returns the array of property names and symbols.
		     */
		    function baseGetAllKeys(object, keysFunc, symbolsFunc) {
		      var result = keysFunc(object);
		      return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
		    }

		    /**
		     * The base implementation of `getTag` without fallbacks for buggy environments.
		     *
		     * @private
		     * @param {*} value The value to query.
		     * @returns {string} Returns the `toStringTag`.
		     */
		    function baseGetTag(value) {
		      if (value == null) {
		        return value === undefined$1 ? undefinedTag : nullTag;
		      }
		      return (symToStringTag && symToStringTag in Object(value))
		        ? getRawTag(value)
		        : objectToString(value);
		    }

		    /**
		     * The base implementation of `_.gt` which doesn't coerce arguments.
		     *
		     * @private
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if `value` is greater than `other`,
		     *  else `false`.
		     */
		    function baseGt(value, other) {
		      return value > other;
		    }

		    /**
		     * The base implementation of `_.has` without support for deep paths.
		     *
		     * @private
		     * @param {Object} [object] The object to query.
		     * @param {Array|string} key The key to check.
		     * @returns {boolean} Returns `true` if `key` exists, else `false`.
		     */
		    function baseHas(object, key) {
		      return object != null && hasOwnProperty.call(object, key);
		    }

		    /**
		     * The base implementation of `_.hasIn` without support for deep paths.
		     *
		     * @private
		     * @param {Object} [object] The object to query.
		     * @param {Array|string} key The key to check.
		     * @returns {boolean} Returns `true` if `key` exists, else `false`.
		     */
		    function baseHasIn(object, key) {
		      return object != null && key in Object(object);
		    }

		    /**
		     * The base implementation of `_.inRange` which doesn't coerce arguments.
		     *
		     * @private
		     * @param {number} number The number to check.
		     * @param {number} start The start of the range.
		     * @param {number} end The end of the range.
		     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
		     */
		    function baseInRange(number, start, end) {
		      return number >= nativeMin(start, end) && number < nativeMax(start, end);
		    }

		    /**
		     * The base implementation of methods like `_.intersection`, without support
		     * for iteratee shorthands, that accepts an array of arrays to inspect.
		     *
		     * @private
		     * @param {Array} arrays The arrays to inspect.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of shared values.
		     */
		    function baseIntersection(arrays, iteratee, comparator) {
		      var includes = comparator ? arrayIncludesWith : arrayIncludes,
		          length = arrays[0].length,
		          othLength = arrays.length,
		          othIndex = othLength,
		          caches = Array(othLength),
		          maxLength = Infinity,
		          result = [];

		      while (othIndex--) {
		        var array = arrays[othIndex];
		        if (othIndex && iteratee) {
		          array = arrayMap(array, baseUnary(iteratee));
		        }
		        maxLength = nativeMin(array.length, maxLength);
		        caches[othIndex] = !comparator && (iteratee || (length >= 120 && array.length >= 120))
		          ? new SetCache(othIndex && array)
		          : undefined$1;
		      }
		      array = arrays[0];

		      var index = -1,
		          seen = caches[0];

		      outer:
		      while (++index < length && result.length < maxLength) {
		        var value = array[index],
		            computed = iteratee ? iteratee(value) : value;

		        value = (comparator || value !== 0) ? value : 0;
		        if (!(seen
		              ? cacheHas(seen, computed)
		              : includes(result, computed, comparator)
		            )) {
		          othIndex = othLength;
		          while (--othIndex) {
		            var cache = caches[othIndex];
		            if (!(cache
		                  ? cacheHas(cache, computed)
		                  : includes(arrays[othIndex], computed, comparator))
		                ) {
		              continue outer;
		            }
		          }
		          if (seen) {
		            seen.push(computed);
		          }
		          result.push(value);
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.invert` and `_.invertBy` which inverts
		     * `object` with values transformed by `iteratee` and set by `setter`.
		     *
		     * @private
		     * @param {Object} object The object to iterate over.
		     * @param {Function} setter The function to set `accumulator` values.
		     * @param {Function} iteratee The iteratee to transform values.
		     * @param {Object} accumulator The initial inverted object.
		     * @returns {Function} Returns `accumulator`.
		     */
		    function baseInverter(object, setter, iteratee, accumulator) {
		      baseForOwn(object, function(value, key, object) {
		        setter(accumulator, iteratee(value), key, object);
		      });
		      return accumulator;
		    }

		    /**
		     * The base implementation of `_.invoke` without support for individual
		     * method arguments.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path of the method to invoke.
		     * @param {Array} args The arguments to invoke the method with.
		     * @returns {*} Returns the result of the invoked method.
		     */
		    function baseInvoke(object, path, args) {
		      path = castPath(path, object);
		      object = parent(object, path);
		      var func = object == null ? object : object[toKey(last(path))];
		      return func == null ? undefined$1 : apply(func, object, args);
		    }

		    /**
		     * The base implementation of `_.isArguments`.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
		     */
		    function baseIsArguments(value) {
		      return isObjectLike(value) && baseGetTag(value) == argsTag;
		    }

		    /**
		     * The base implementation of `_.isArrayBuffer` without Node.js optimizations.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
		     */
		    function baseIsArrayBuffer(value) {
		      return isObjectLike(value) && baseGetTag(value) == arrayBufferTag;
		    }

		    /**
		     * The base implementation of `_.isDate` without Node.js optimizations.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
		     */
		    function baseIsDate(value) {
		      return isObjectLike(value) && baseGetTag(value) == dateTag;
		    }

		    /**
		     * The base implementation of `_.isEqual` which supports partial comparisons
		     * and tracks traversed objects.
		     *
		     * @private
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @param {boolean} bitmask The bitmask flags.
		     *  1 - Unordered comparison
		     *  2 - Partial comparison
		     * @param {Function} [customizer] The function to customize comparisons.
		     * @param {Object} [stack] Tracks traversed `value` and `other` objects.
		     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
		     */
		    function baseIsEqual(value, other, bitmask, customizer, stack) {
		      if (value === other) {
		        return true;
		      }
		      if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
		        return value !== value && other !== other;
		      }
		      return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
		    }

		    /**
		     * A specialized version of `baseIsEqual` for arrays and objects which performs
		     * deep comparisons and tracks traversed objects enabling objects with circular
		     * references to be compared.
		     *
		     * @private
		     * @param {Object} object The object to compare.
		     * @param {Object} other The other object to compare.
		     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
		     * @param {Function} customizer The function to customize comparisons.
		     * @param {Function} equalFunc The function to determine equivalents of values.
		     * @param {Object} [stack] Tracks traversed `object` and `other` objects.
		     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
		     */
		    function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
		      var objIsArr = isArray(object),
		          othIsArr = isArray(other),
		          objTag = objIsArr ? arrayTag : getTag(object),
		          othTag = othIsArr ? arrayTag : getTag(other);

		      objTag = objTag == argsTag ? objectTag : objTag;
		      othTag = othTag == argsTag ? objectTag : othTag;

		      var objIsObj = objTag == objectTag,
		          othIsObj = othTag == objectTag,
		          isSameTag = objTag == othTag;

		      if (isSameTag && isBuffer(object)) {
		        if (!isBuffer(other)) {
		          return false;
		        }
		        objIsArr = true;
		        objIsObj = false;
		      }
		      if (isSameTag && !objIsObj) {
		        stack || (stack = new Stack);
		        return (objIsArr || isTypedArray(object))
		          ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
		          : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
		      }
		      if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
		        var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
		            othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

		        if (objIsWrapped || othIsWrapped) {
		          var objUnwrapped = objIsWrapped ? object.value() : object,
		              othUnwrapped = othIsWrapped ? other.value() : other;

		          stack || (stack = new Stack);
		          return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
		        }
		      }
		      if (!isSameTag) {
		        return false;
		      }
		      stack || (stack = new Stack);
		      return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
		    }

		    /**
		     * The base implementation of `_.isMap` without Node.js optimizations.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
		     */
		    function baseIsMap(value) {
		      return isObjectLike(value) && getTag(value) == mapTag;
		    }

		    /**
		     * The base implementation of `_.isMatch` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Object} object The object to inspect.
		     * @param {Object} source The object of property values to match.
		     * @param {Array} matchData The property names, values, and compare flags to match.
		     * @param {Function} [customizer] The function to customize comparisons.
		     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
		     */
		    function baseIsMatch(object, source, matchData, customizer) {
		      var index = matchData.length,
		          length = index,
		          noCustomizer = !customizer;

		      if (object == null) {
		        return !length;
		      }
		      object = Object(object);
		      while (index--) {
		        var data = matchData[index];
		        if ((noCustomizer && data[2])
		              ? data[1] !== object[data[0]]
		              : !(data[0] in object)
		            ) {
		          return false;
		        }
		      }
		      while (++index < length) {
		        data = matchData[index];
		        var key = data[0],
		            objValue = object[key],
		            srcValue = data[1];

		        if (noCustomizer && data[2]) {
		          if (objValue === undefined$1 && !(key in object)) {
		            return false;
		          }
		        } else {
		          var stack = new Stack;
		          if (customizer) {
		            var result = customizer(objValue, srcValue, key, object, source, stack);
		          }
		          if (!(result === undefined$1
		                ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG, customizer, stack)
		                : result
		              )) {
		            return false;
		          }
		        }
		      }
		      return true;
		    }

		    /**
		     * The base implementation of `_.isNative` without bad shim checks.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a native function,
		     *  else `false`.
		     */
		    function baseIsNative(value) {
		      if (!isObject(value) || isMasked(value)) {
		        return false;
		      }
		      var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
		      return pattern.test(toSource(value));
		    }

		    /**
		     * The base implementation of `_.isRegExp` without Node.js optimizations.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
		     */
		    function baseIsRegExp(value) {
		      return isObjectLike(value) && baseGetTag(value) == regexpTag;
		    }

		    /**
		     * The base implementation of `_.isSet` without Node.js optimizations.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
		     */
		    function baseIsSet(value) {
		      return isObjectLike(value) && getTag(value) == setTag;
		    }

		    /**
		     * The base implementation of `_.isTypedArray` without Node.js optimizations.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
		     */
		    function baseIsTypedArray(value) {
		      return isObjectLike(value) &&
		        isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
		    }

		    /**
		     * The base implementation of `_.iteratee`.
		     *
		     * @private
		     * @param {*} [value=_.identity] The value to convert to an iteratee.
		     * @returns {Function} Returns the iteratee.
		     */
		    function baseIteratee(value) {
		      // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
		      // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
		      if (typeof value == 'function') {
		        return value;
		      }
		      if (value == null) {
		        return identity;
		      }
		      if (typeof value == 'object') {
		        return isArray(value)
		          ? baseMatchesProperty(value[0], value[1])
		          : baseMatches(value);
		      }
		      return property(value);
		    }

		    /**
		     * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names.
		     */
		    function baseKeys(object) {
		      if (!isPrototype(object)) {
		        return nativeKeys(object);
		      }
		      var result = [];
		      for (var key in Object(object)) {
		        if (hasOwnProperty.call(object, key) && key != 'constructor') {
		          result.push(key);
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names.
		     */
		    function baseKeysIn(object) {
		      if (!isObject(object)) {
		        return nativeKeysIn(object);
		      }
		      var isProto = isPrototype(object),
		          result = [];

		      for (var key in object) {
		        if (!(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
		          result.push(key);
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.lt` which doesn't coerce arguments.
		     *
		     * @private
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if `value` is less than `other`,
		     *  else `false`.
		     */
		    function baseLt(value, other) {
		      return value < other;
		    }

		    /**
		     * The base implementation of `_.map` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} iteratee The function invoked per iteration.
		     * @returns {Array} Returns the new mapped array.
		     */
		    function baseMap(collection, iteratee) {
		      var index = -1,
		          result = isArrayLike(collection) ? Array(collection.length) : [];

		      baseEach(collection, function(value, key, collection) {
		        result[++index] = iteratee(value, key, collection);
		      });
		      return result;
		    }

		    /**
		     * The base implementation of `_.matches` which doesn't clone `source`.
		     *
		     * @private
		     * @param {Object} source The object of property values to match.
		     * @returns {Function} Returns the new spec function.
		     */
		    function baseMatches(source) {
		      var matchData = getMatchData(source);
		      if (matchData.length == 1 && matchData[0][2]) {
		        return matchesStrictComparable(matchData[0][0], matchData[0][1]);
		      }
		      return function(object) {
		        return object === source || baseIsMatch(object, source, matchData);
		      };
		    }

		    /**
		     * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
		     *
		     * @private
		     * @param {string} path The path of the property to get.
		     * @param {*} srcValue The value to match.
		     * @returns {Function} Returns the new spec function.
		     */
		    function baseMatchesProperty(path, srcValue) {
		      if (isKey(path) && isStrictComparable(srcValue)) {
		        return matchesStrictComparable(toKey(path), srcValue);
		      }
		      return function(object) {
		        var objValue = get(object, path);
		        return (objValue === undefined$1 && objValue === srcValue)
		          ? hasIn(object, path)
		          : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
		      };
		    }

		    /**
		     * The base implementation of `_.merge` without support for multiple sources.
		     *
		     * @private
		     * @param {Object} object The destination object.
		     * @param {Object} source The source object.
		     * @param {number} srcIndex The index of `source`.
		     * @param {Function} [customizer] The function to customize merged values.
		     * @param {Object} [stack] Tracks traversed source values and their merged
		     *  counterparts.
		     */
		    function baseMerge(object, source, srcIndex, customizer, stack) {
		      if (object === source) {
		        return;
		      }
		      baseFor(source, function(srcValue, key) {
		        stack || (stack = new Stack);
		        if (isObject(srcValue)) {
		          baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
		        }
		        else {
		          var newValue = customizer
		            ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
		            : undefined$1;

		          if (newValue === undefined$1) {
		            newValue = srcValue;
		          }
		          assignMergeValue(object, key, newValue);
		        }
		      }, keysIn);
		    }

		    /**
		     * A specialized version of `baseMerge` for arrays and objects which performs
		     * deep merges and tracks traversed objects enabling objects with circular
		     * references to be merged.
		     *
		     * @private
		     * @param {Object} object The destination object.
		     * @param {Object} source The source object.
		     * @param {string} key The key of the value to merge.
		     * @param {number} srcIndex The index of `source`.
		     * @param {Function} mergeFunc The function to merge values.
		     * @param {Function} [customizer] The function to customize assigned values.
		     * @param {Object} [stack] Tracks traversed source values and their merged
		     *  counterparts.
		     */
		    function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
		      var objValue = safeGet(object, key),
		          srcValue = safeGet(source, key),
		          stacked = stack.get(srcValue);

		      if (stacked) {
		        assignMergeValue(object, key, stacked);
		        return;
		      }
		      var newValue = customizer
		        ? customizer(objValue, srcValue, (key + ''), object, source, stack)
		        : undefined$1;

		      var isCommon = newValue === undefined$1;

		      if (isCommon) {
		        var isArr = isArray(srcValue),
		            isBuff = !isArr && isBuffer(srcValue),
		            isTyped = !isArr && !isBuff && isTypedArray(srcValue);

		        newValue = srcValue;
		        if (isArr || isBuff || isTyped) {
		          if (isArray(objValue)) {
		            newValue = objValue;
		          }
		          else if (isArrayLikeObject(objValue)) {
		            newValue = copyArray(objValue);
		          }
		          else if (isBuff) {
		            isCommon = false;
		            newValue = cloneBuffer(srcValue, true);
		          }
		          else if (isTyped) {
		            isCommon = false;
		            newValue = cloneTypedArray(srcValue, true);
		          }
		          else {
		            newValue = [];
		          }
		        }
		        else if (isPlainObject(srcValue) || isArguments(srcValue)) {
		          newValue = objValue;
		          if (isArguments(objValue)) {
		            newValue = toPlainObject(objValue);
		          }
		          else if (!isObject(objValue) || isFunction(objValue)) {
		            newValue = initCloneObject(srcValue);
		          }
		        }
		        else {
		          isCommon = false;
		        }
		      }
		      if (isCommon) {
		        // Recursively merge objects and arrays (susceptible to call stack limits).
		        stack.set(srcValue, newValue);
		        mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
		        stack['delete'](srcValue);
		      }
		      assignMergeValue(object, key, newValue);
		    }

		    /**
		     * The base implementation of `_.nth` which doesn't coerce arguments.
		     *
		     * @private
		     * @param {Array} array The array to query.
		     * @param {number} n The index of the element to return.
		     * @returns {*} Returns the nth element of `array`.
		     */
		    function baseNth(array, n) {
		      var length = array.length;
		      if (!length) {
		        return;
		      }
		      n += n < 0 ? length : 0;
		      return isIndex(n, length) ? array[n] : undefined$1;
		    }

		    /**
		     * The base implementation of `_.orderBy` without param guards.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
		     * @param {string[]} orders The sort orders of `iteratees`.
		     * @returns {Array} Returns the new sorted array.
		     */
		    function baseOrderBy(collection, iteratees, orders) {
		      if (iteratees.length) {
		        iteratees = arrayMap(iteratees, function(iteratee) {
		          if (isArray(iteratee)) {
		            return function(value) {
		              return baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
		            };
		          }
		          return iteratee;
		        });
		      } else {
		        iteratees = [identity];
		      }

		      var index = -1;
		      iteratees = arrayMap(iteratees, baseUnary(getIteratee()));

		      var result = baseMap(collection, function(value, key, collection) {
		        var criteria = arrayMap(iteratees, function(iteratee) {
		          return iteratee(value);
		        });
		        return { 'criteria': criteria, 'index': ++index, 'value': value };
		      });

		      return baseSortBy(result, function(object, other) {
		        return compareMultiple(object, other, orders);
		      });
		    }

		    /**
		     * The base implementation of `_.pick` without support for individual
		     * property identifiers.
		     *
		     * @private
		     * @param {Object} object The source object.
		     * @param {string[]} paths The property paths to pick.
		     * @returns {Object} Returns the new object.
		     */
		    function basePick(object, paths) {
		      return basePickBy(object, paths, function(value, path) {
		        return hasIn(object, path);
		      });
		    }

		    /**
		     * The base implementation of  `_.pickBy` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Object} object The source object.
		     * @param {string[]} paths The property paths to pick.
		     * @param {Function} predicate The function invoked per property.
		     * @returns {Object} Returns the new object.
		     */
		    function basePickBy(object, paths, predicate) {
		      var index = -1,
		          length = paths.length,
		          result = {};

		      while (++index < length) {
		        var path = paths[index],
		            value = baseGet(object, path);

		        if (predicate(value, path)) {
		          baseSet(result, castPath(path, object), value);
		        }
		      }
		      return result;
		    }

		    /**
		     * A specialized version of `baseProperty` which supports deep paths.
		     *
		     * @private
		     * @param {Array|string} path The path of the property to get.
		     * @returns {Function} Returns the new accessor function.
		     */
		    function basePropertyDeep(path) {
		      return function(object) {
		        return baseGet(object, path);
		      };
		    }

		    /**
		     * The base implementation of `_.pullAllBy` without support for iteratee
		     * shorthands.
		     *
		     * @private
		     * @param {Array} array The array to modify.
		     * @param {Array} values The values to remove.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns `array`.
		     */
		    function basePullAll(array, values, iteratee, comparator) {
		      var indexOf = comparator ? baseIndexOfWith : baseIndexOf,
		          index = -1,
		          length = values.length,
		          seen = array;

		      if (array === values) {
		        values = copyArray(values);
		      }
		      if (iteratee) {
		        seen = arrayMap(array, baseUnary(iteratee));
		      }
		      while (++index < length) {
		        var fromIndex = 0,
		            value = values[index],
		            computed = iteratee ? iteratee(value) : value;

		        while ((fromIndex = indexOf(seen, computed, fromIndex, comparator)) > -1) {
		          if (seen !== array) {
		            splice.call(seen, fromIndex, 1);
		          }
		          splice.call(array, fromIndex, 1);
		        }
		      }
		      return array;
		    }

		    /**
		     * The base implementation of `_.pullAt` without support for individual
		     * indexes or capturing the removed elements.
		     *
		     * @private
		     * @param {Array} array The array to modify.
		     * @param {number[]} indexes The indexes of elements to remove.
		     * @returns {Array} Returns `array`.
		     */
		    function basePullAt(array, indexes) {
		      var length = array ? indexes.length : 0,
		          lastIndex = length - 1;

		      while (length--) {
		        var index = indexes[length];
		        if (length == lastIndex || index !== previous) {
		          var previous = index;
		          if (isIndex(index)) {
		            splice.call(array, index, 1);
		          } else {
		            baseUnset(array, index);
		          }
		        }
		      }
		      return array;
		    }

		    /**
		     * The base implementation of `_.random` without support for returning
		     * floating-point numbers.
		     *
		     * @private
		     * @param {number} lower The lower bound.
		     * @param {number} upper The upper bound.
		     * @returns {number} Returns the random number.
		     */
		    function baseRandom(lower, upper) {
		      return lower + nativeFloor(nativeRandom() * (upper - lower + 1));
		    }

		    /**
		     * The base implementation of `_.range` and `_.rangeRight` which doesn't
		     * coerce arguments.
		     *
		     * @private
		     * @param {number} start The start of the range.
		     * @param {number} end The end of the range.
		     * @param {number} step The value to increment or decrement by.
		     * @param {boolean} [fromRight] Specify iterating from right to left.
		     * @returns {Array} Returns the range of numbers.
		     */
		    function baseRange(start, end, step, fromRight) {
		      var index = -1,
		          length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
		          result = Array(length);

		      while (length--) {
		        result[fromRight ? length : ++index] = start;
		        start += step;
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.repeat` which doesn't coerce arguments.
		     *
		     * @private
		     * @param {string} string The string to repeat.
		     * @param {number} n The number of times to repeat the string.
		     * @returns {string} Returns the repeated string.
		     */
		    function baseRepeat(string, n) {
		      var result = '';
		      if (!string || n < 1 || n > MAX_SAFE_INTEGER) {
		        return result;
		      }
		      // Leverage the exponentiation by squaring algorithm for a faster repeat.
		      // See https://en.wikipedia.org/wiki/Exponentiation_by_squaring for more details.
		      do {
		        if (n % 2) {
		          result += string;
		        }
		        n = nativeFloor(n / 2);
		        if (n) {
		          string += string;
		        }
		      } while (n);

		      return result;
		    }

		    /**
		     * The base implementation of `_.rest` which doesn't validate or coerce arguments.
		     *
		     * @private
		     * @param {Function} func The function to apply a rest parameter to.
		     * @param {number} [start=func.length-1] The start position of the rest parameter.
		     * @returns {Function} Returns the new function.
		     */
		    function baseRest(func, start) {
		      return setToString(overRest(func, start, identity), func + '');
		    }

		    /**
		     * The base implementation of `_.sample`.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to sample.
		     * @returns {*} Returns the random element.
		     */
		    function baseSample(collection) {
		      return arraySample(values(collection));
		    }

		    /**
		     * The base implementation of `_.sampleSize` without param guards.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to sample.
		     * @param {number} n The number of elements to sample.
		     * @returns {Array} Returns the random elements.
		     */
		    function baseSampleSize(collection, n) {
		      var array = values(collection);
		      return shuffleSelf(array, baseClamp(n, 0, array.length));
		    }

		    /**
		     * The base implementation of `_.set`.
		     *
		     * @private
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to set.
		     * @param {*} value The value to set.
		     * @param {Function} [customizer] The function to customize path creation.
		     * @returns {Object} Returns `object`.
		     */
		    function baseSet(object, path, value, customizer) {
		      if (!isObject(object)) {
		        return object;
		      }
		      path = castPath(path, object);

		      var index = -1,
		          length = path.length,
		          lastIndex = length - 1,
		          nested = object;

		      while (nested != null && ++index < length) {
		        var key = toKey(path[index]),
		            newValue = value;

		        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
		          return object;
		        }

		        if (index != lastIndex) {
		          var objValue = nested[key];
		          newValue = customizer ? customizer(objValue, key, nested) : undefined$1;
		          if (newValue === undefined$1) {
		            newValue = isObject(objValue)
		              ? objValue
		              : (isIndex(path[index + 1]) ? [] : {});
		          }
		        }
		        assignValue(nested, key, newValue);
		        nested = nested[key];
		      }
		      return object;
		    }

		    /**
		     * The base implementation of `setData` without support for hot loop shorting.
		     *
		     * @private
		     * @param {Function} func The function to associate metadata with.
		     * @param {*} data The metadata.
		     * @returns {Function} Returns `func`.
		     */
		    var baseSetData = !metaMap ? identity : function(func, data) {
		      metaMap.set(func, data);
		      return func;
		    };

		    /**
		     * The base implementation of `setToString` without support for hot loop shorting.
		     *
		     * @private
		     * @param {Function} func The function to modify.
		     * @param {Function} string The `toString` result.
		     * @returns {Function} Returns `func`.
		     */
		    var baseSetToString = !defineProperty ? identity : function(func, string) {
		      return defineProperty(func, 'toString', {
		        'configurable': true,
		        'enumerable': false,
		        'value': constant(string),
		        'writable': true
		      });
		    };

		    /**
		     * The base implementation of `_.shuffle`.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to shuffle.
		     * @returns {Array} Returns the new shuffled array.
		     */
		    function baseShuffle(collection) {
		      return shuffleSelf(values(collection));
		    }

		    /**
		     * The base implementation of `_.slice` without an iteratee call guard.
		     *
		     * @private
		     * @param {Array} array The array to slice.
		     * @param {number} [start=0] The start position.
		     * @param {number} [end=array.length] The end position.
		     * @returns {Array} Returns the slice of `array`.
		     */
		    function baseSlice(array, start, end) {
		      var index = -1,
		          length = array.length;

		      if (start < 0) {
		        start = -start > length ? 0 : (length + start);
		      }
		      end = end > length ? length : end;
		      if (end < 0) {
		        end += length;
		      }
		      length = start > end ? 0 : ((end - start) >>> 0);
		      start >>>= 0;

		      var result = Array(length);
		      while (++index < length) {
		        result[index] = array[index + start];
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.some` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} predicate The function invoked per iteration.
		     * @returns {boolean} Returns `true` if any element passes the predicate check,
		     *  else `false`.
		     */
		    function baseSome(collection, predicate) {
		      var result;

		      baseEach(collection, function(value, index, collection) {
		        result = predicate(value, index, collection);
		        return !result;
		      });
		      return !!result;
		    }

		    /**
		     * The base implementation of `_.sortedIndex` and `_.sortedLastIndex` which
		     * performs a binary search of `array` to determine the index at which `value`
		     * should be inserted into `array` in order to maintain its sort order.
		     *
		     * @private
		     * @param {Array} array The sorted array to inspect.
		     * @param {*} value The value to evaluate.
		     * @param {boolean} [retHighest] Specify returning the highest qualified index.
		     * @returns {number} Returns the index at which `value` should be inserted
		     *  into `array`.
		     */
		    function baseSortedIndex(array, value, retHighest) {
		      var low = 0,
		          high = array == null ? low : array.length;

		      if (typeof value == 'number' && value === value && high <= HALF_MAX_ARRAY_LENGTH) {
		        while (low < high) {
		          var mid = (low + high) >>> 1,
		              computed = array[mid];

		          if (computed !== null && !isSymbol(computed) &&
		              (retHighest ? (computed <= value) : (computed < value))) {
		            low = mid + 1;
		          } else {
		            high = mid;
		          }
		        }
		        return high;
		      }
		      return baseSortedIndexBy(array, value, identity, retHighest);
		    }

		    /**
		     * The base implementation of `_.sortedIndexBy` and `_.sortedLastIndexBy`
		     * which invokes `iteratee` for `value` and each element of `array` to compute
		     * their sort ranking. The iteratee is invoked with one argument; (value).
		     *
		     * @private
		     * @param {Array} array The sorted array to inspect.
		     * @param {*} value The value to evaluate.
		     * @param {Function} iteratee The iteratee invoked per element.
		     * @param {boolean} [retHighest] Specify returning the highest qualified index.
		     * @returns {number} Returns the index at which `value` should be inserted
		     *  into `array`.
		     */
		    function baseSortedIndexBy(array, value, iteratee, retHighest) {
		      var low = 0,
		          high = array == null ? 0 : array.length;
		      if (high === 0) {
		        return 0;
		      }

		      value = iteratee(value);
		      var valIsNaN = value !== value,
		          valIsNull = value === null,
		          valIsSymbol = isSymbol(value),
		          valIsUndefined = value === undefined$1;

		      while (low < high) {
		        var mid = nativeFloor((low + high) / 2),
		            computed = iteratee(array[mid]),
		            othIsDefined = computed !== undefined$1,
		            othIsNull = computed === null,
		            othIsReflexive = computed === computed,
		            othIsSymbol = isSymbol(computed);

		        if (valIsNaN) {
		          var setLow = retHighest || othIsReflexive;
		        } else if (valIsUndefined) {
		          setLow = othIsReflexive && (retHighest || othIsDefined);
		        } else if (valIsNull) {
		          setLow = othIsReflexive && othIsDefined && (retHighest || !othIsNull);
		        } else if (valIsSymbol) {
		          setLow = othIsReflexive && othIsDefined && !othIsNull && (retHighest || !othIsSymbol);
		        } else if (othIsNull || othIsSymbol) {
		          setLow = false;
		        } else {
		          setLow = retHighest ? (computed <= value) : (computed < value);
		        }
		        if (setLow) {
		          low = mid + 1;
		        } else {
		          high = mid;
		        }
		      }
		      return nativeMin(high, MAX_ARRAY_INDEX);
		    }

		    /**
		     * The base implementation of `_.sortedUniq` and `_.sortedUniqBy` without
		     * support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array} array The array to inspect.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @returns {Array} Returns the new duplicate free array.
		     */
		    function baseSortedUniq(array, iteratee) {
		      var index = -1,
		          length = array.length,
		          resIndex = 0,
		          result = [];

		      while (++index < length) {
		        var value = array[index],
		            computed = iteratee ? iteratee(value) : value;

		        if (!index || !eq(computed, seen)) {
		          var seen = computed;
		          result[resIndex++] = value === 0 ? 0 : value;
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.toNumber` which doesn't ensure correct
		     * conversions of binary, hexadecimal, or octal string values.
		     *
		     * @private
		     * @param {*} value The value to process.
		     * @returns {number} Returns the number.
		     */
		    function baseToNumber(value) {
		      if (typeof value == 'number') {
		        return value;
		      }
		      if (isSymbol(value)) {
		        return NAN;
		      }
		      return +value;
		    }

		    /**
		     * The base implementation of `_.toString` which doesn't convert nullish
		     * values to empty strings.
		     *
		     * @private
		     * @param {*} value The value to process.
		     * @returns {string} Returns the string.
		     */
		    function baseToString(value) {
		      // Exit early for strings to avoid a performance hit in some environments.
		      if (typeof value == 'string') {
		        return value;
		      }
		      if (isArray(value)) {
		        // Recursively convert values (susceptible to call stack limits).
		        return arrayMap(value, baseToString) + '';
		      }
		      if (isSymbol(value)) {
		        return symbolToString ? symbolToString.call(value) : '';
		      }
		      var result = (value + '');
		      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
		    }

		    /**
		     * The base implementation of `_.uniqBy` without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array} array The array to inspect.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new duplicate free array.
		     */
		    function baseUniq(array, iteratee, comparator) {
		      var index = -1,
		          includes = arrayIncludes,
		          length = array.length,
		          isCommon = true,
		          result = [],
		          seen = result;

		      if (comparator) {
		        isCommon = false;
		        includes = arrayIncludesWith;
		      }
		      else if (length >= LARGE_ARRAY_SIZE) {
		        var set = iteratee ? null : createSet(array);
		        if (set) {
		          return setToArray(set);
		        }
		        isCommon = false;
		        includes = cacheHas;
		        seen = new SetCache;
		      }
		      else {
		        seen = iteratee ? [] : result;
		      }
		      outer:
		      while (++index < length) {
		        var value = array[index],
		            computed = iteratee ? iteratee(value) : value;

		        value = (comparator || value !== 0) ? value : 0;
		        if (isCommon && computed === computed) {
		          var seenIndex = seen.length;
		          while (seenIndex--) {
		            if (seen[seenIndex] === computed) {
		              continue outer;
		            }
		          }
		          if (iteratee) {
		            seen.push(computed);
		          }
		          result.push(value);
		        }
		        else if (!includes(seen, computed, comparator)) {
		          if (seen !== result) {
		            seen.push(computed);
		          }
		          result.push(value);
		        }
		      }
		      return result;
		    }

		    /**
		     * The base implementation of `_.unset`.
		     *
		     * @private
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The property path to unset.
		     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
		     */
		    function baseUnset(object, path) {
		      path = castPath(path, object);

		      // Prevent prototype pollution:
		      // https://github.com/lodash/lodash/security/advisories/GHSA-xxjr-mmjv-4gpg
		      // https://github.com/lodash/lodash/security/advisories/GHSA-f23m-r3pf-42rh
		      var index = -1,
		          length = path.length;

		      if (!length) {
		        return true;
		      }

		      while (++index < length) {
		        var key = toKey(path[index]);

		        // Always block "__proto__" anywhere in the path if it's not expected
		        if (key === '__proto__' && !hasOwnProperty.call(object, '__proto__')) {
		          return false;
		        }

		        // Block constructor/prototype as non-terminal traversal keys to prevent
		        // escaping the object graph into built-in constructors and prototypes.
		        if ((key === 'constructor' || key === 'prototype') && index < length - 1) {
		          return false;
		        }
		      }

		      var obj = parent(object, path);
		      return obj == null || delete obj[toKey(last(path))];
		    }

		    /**
		     * The base implementation of `_.update`.
		     *
		     * @private
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to update.
		     * @param {Function} updater The function to produce the updated value.
		     * @param {Function} [customizer] The function to customize path creation.
		     * @returns {Object} Returns `object`.
		     */
		    function baseUpdate(object, path, updater, customizer) {
		      return baseSet(object, path, updater(baseGet(object, path)), customizer);
		    }

		    /**
		     * The base implementation of methods like `_.dropWhile` and `_.takeWhile`
		     * without support for iteratee shorthands.
		     *
		     * @private
		     * @param {Array} array The array to query.
		     * @param {Function} predicate The function invoked per iteration.
		     * @param {boolean} [isDrop] Specify dropping elements instead of taking them.
		     * @param {boolean} [fromRight] Specify iterating from right to left.
		     * @returns {Array} Returns the slice of `array`.
		     */
		    function baseWhile(array, predicate, isDrop, fromRight) {
		      var length = array.length,
		          index = fromRight ? length : -1;

		      while ((fromRight ? index-- : ++index < length) &&
		        predicate(array[index], index, array)) {}

		      return isDrop
		        ? baseSlice(array, (fromRight ? 0 : index), (fromRight ? index + 1 : length))
		        : baseSlice(array, (fromRight ? index + 1 : 0), (fromRight ? length : index));
		    }

		    /**
		     * The base implementation of `wrapperValue` which returns the result of
		     * performing a sequence of actions on the unwrapped `value`, where each
		     * successive action is supplied the return value of the previous.
		     *
		     * @private
		     * @param {*} value The unwrapped value.
		     * @param {Array} actions Actions to perform to resolve the unwrapped value.
		     * @returns {*} Returns the resolved value.
		     */
		    function baseWrapperValue(value, actions) {
		      var result = value;
		      if (result instanceof LazyWrapper) {
		        result = result.value();
		      }
		      return arrayReduce(actions, function(result, action) {
		        return action.func.apply(action.thisArg, arrayPush([result], action.args));
		      }, result);
		    }

		    /**
		     * The base implementation of methods like `_.xor`, without support for
		     * iteratee shorthands, that accepts an array of arrays to inspect.
		     *
		     * @private
		     * @param {Array} arrays The arrays to inspect.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of values.
		     */
		    function baseXor(arrays, iteratee, comparator) {
		      var length = arrays.length;
		      if (length < 2) {
		        return length ? baseUniq(arrays[0]) : [];
		      }
		      var index = -1,
		          result = Array(length);

		      while (++index < length) {
		        var array = arrays[index],
		            othIndex = -1;

		        while (++othIndex < length) {
		          if (othIndex != index) {
		            result[index] = baseDifference(result[index] || array, arrays[othIndex], iteratee, comparator);
		          }
		        }
		      }
		      return baseUniq(baseFlatten(result, 1), iteratee, comparator);
		    }

		    /**
		     * This base implementation of `_.zipObject` which assigns values using `assignFunc`.
		     *
		     * @private
		     * @param {Array} props The property identifiers.
		     * @param {Array} values The property values.
		     * @param {Function} assignFunc The function to assign values.
		     * @returns {Object} Returns the new object.
		     */
		    function baseZipObject(props, values, assignFunc) {
		      var index = -1,
		          length = props.length,
		          valsLength = values.length,
		          result = {};

		      while (++index < length) {
		        var value = index < valsLength ? values[index] : undefined$1;
		        assignFunc(result, props[index], value);
		      }
		      return result;
		    }

		    /**
		     * Casts `value` to an empty array if it's not an array like object.
		     *
		     * @private
		     * @param {*} value The value to inspect.
		     * @returns {Array|Object} Returns the cast array-like object.
		     */
		    function castArrayLikeObject(value) {
		      return isArrayLikeObject(value) ? value : [];
		    }

		    /**
		     * Casts `value` to `identity` if it's not a function.
		     *
		     * @private
		     * @param {*} value The value to inspect.
		     * @returns {Function} Returns cast function.
		     */
		    function castFunction(value) {
		      return typeof value == 'function' ? value : identity;
		    }

		    /**
		     * Casts `value` to a path array if it's not one.
		     *
		     * @private
		     * @param {*} value The value to inspect.
		     * @param {Object} [object] The object to query keys on.
		     * @returns {Array} Returns the cast property path array.
		     */
		    function castPath(value, object) {
		      if (isArray(value)) {
		        return value;
		      }
		      return isKey(value, object) ? [value] : stringToPath(toString(value));
		    }

		    /**
		     * A `baseRest` alias which can be replaced with `identity` by module
		     * replacement plugins.
		     *
		     * @private
		     * @type {Function}
		     * @param {Function} func The function to apply a rest parameter to.
		     * @returns {Function} Returns the new function.
		     */
		    var castRest = baseRest;

		    /**
		     * Casts `array` to a slice if it's needed.
		     *
		     * @private
		     * @param {Array} array The array to inspect.
		     * @param {number} start The start position.
		     * @param {number} [end=array.length] The end position.
		     * @returns {Array} Returns the cast slice.
		     */
		    function castSlice(array, start, end) {
		      var length = array.length;
		      end = end === undefined$1 ? length : end;
		      return (!start && end >= length) ? array : baseSlice(array, start, end);
		    }

		    /**
		     * A simple wrapper around the global [`clearTimeout`](https://mdn.io/clearTimeout).
		     *
		     * @private
		     * @param {number|Object} id The timer id or timeout object of the timer to clear.
		     */
		    var clearTimeout = ctxClearTimeout || function(id) {
		      return root.clearTimeout(id);
		    };

		    /**
		     * Creates a clone of  `buffer`.
		     *
		     * @private
		     * @param {Buffer} buffer The buffer to clone.
		     * @param {boolean} [isDeep] Specify a deep clone.
		     * @returns {Buffer} Returns the cloned buffer.
		     */
		    function cloneBuffer(buffer, isDeep) {
		      if (isDeep) {
		        return buffer.slice();
		      }
		      var length = buffer.length,
		          result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

		      buffer.copy(result);
		      return result;
		    }

		    /**
		     * Creates a clone of `arrayBuffer`.
		     *
		     * @private
		     * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
		     * @returns {ArrayBuffer} Returns the cloned array buffer.
		     */
		    function cloneArrayBuffer(arrayBuffer) {
		      var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
		      new Uint8Array(result).set(new Uint8Array(arrayBuffer));
		      return result;
		    }

		    /**
		     * Creates a clone of `dataView`.
		     *
		     * @private
		     * @param {Object} dataView The data view to clone.
		     * @param {boolean} [isDeep] Specify a deep clone.
		     * @returns {Object} Returns the cloned data view.
		     */
		    function cloneDataView(dataView, isDeep) {
		      var buffer = isDeep ? cloneArrayBuffer(dataView.buffer) : dataView.buffer;
		      return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
		    }

		    /**
		     * Creates a clone of `regexp`.
		     *
		     * @private
		     * @param {Object} regexp The regexp to clone.
		     * @returns {Object} Returns the cloned regexp.
		     */
		    function cloneRegExp(regexp) {
		      var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
		      result.lastIndex = regexp.lastIndex;
		      return result;
		    }

		    /**
		     * Creates a clone of the `symbol` object.
		     *
		     * @private
		     * @param {Object} symbol The symbol object to clone.
		     * @returns {Object} Returns the cloned symbol object.
		     */
		    function cloneSymbol(symbol) {
		      return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
		    }

		    /**
		     * Creates a clone of `typedArray`.
		     *
		     * @private
		     * @param {Object} typedArray The typed array to clone.
		     * @param {boolean} [isDeep] Specify a deep clone.
		     * @returns {Object} Returns the cloned typed array.
		     */
		    function cloneTypedArray(typedArray, isDeep) {
		      var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
		      return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
		    }

		    /**
		     * Compares values to sort them in ascending order.
		     *
		     * @private
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {number} Returns the sort order indicator for `value`.
		     */
		    function compareAscending(value, other) {
		      if (value !== other) {
		        var valIsDefined = value !== undefined$1,
		            valIsNull = value === null,
		            valIsReflexive = value === value,
		            valIsSymbol = isSymbol(value);

		        var othIsDefined = other !== undefined$1,
		            othIsNull = other === null,
		            othIsReflexive = other === other,
		            othIsSymbol = isSymbol(other);

		        if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
		            (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
		            (valIsNull && othIsDefined && othIsReflexive) ||
		            (!valIsDefined && othIsReflexive) ||
		            !valIsReflexive) {
		          return 1;
		        }
		        if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
		            (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
		            (othIsNull && valIsDefined && valIsReflexive) ||
		            (!othIsDefined && valIsReflexive) ||
		            !othIsReflexive) {
		          return -1;
		        }
		      }
		      return 0;
		    }

		    /**
		     * Used by `_.orderBy` to compare multiple properties of a value to another
		     * and stable sort them.
		     *
		     * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
		     * specify an order of "desc" for descending or "asc" for ascending sort order
		     * of corresponding values.
		     *
		     * @private
		     * @param {Object} object The object to compare.
		     * @param {Object} other The other object to compare.
		     * @param {boolean[]|string[]} orders The order to sort by for each property.
		     * @returns {number} Returns the sort order indicator for `object`.
		     */
		    function compareMultiple(object, other, orders) {
		      var index = -1,
		          objCriteria = object.criteria,
		          othCriteria = other.criteria,
		          length = objCriteria.length,
		          ordersLength = orders.length;

		      while (++index < length) {
		        var result = compareAscending(objCriteria[index], othCriteria[index]);
		        if (result) {
		          if (index >= ordersLength) {
		            return result;
		          }
		          var order = orders[index];
		          return result * (order == 'desc' ? -1 : 1);
		        }
		      }
		      // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
		      // that causes it, under certain circumstances, to provide the same value for
		      // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
		      // for more details.
		      //
		      // This also ensures a stable sort in V8 and other engines.
		      // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
		      return object.index - other.index;
		    }

		    /**
		     * Creates an array that is the composition of partially applied arguments,
		     * placeholders, and provided arguments into a single array of arguments.
		     *
		     * @private
		     * @param {Array} args The provided arguments.
		     * @param {Array} partials The arguments to prepend to those provided.
		     * @param {Array} holders The `partials` placeholder indexes.
		     * @params {boolean} [isCurried] Specify composing for a curried function.
		     * @returns {Array} Returns the new array of composed arguments.
		     */
		    function composeArgs(args, partials, holders, isCurried) {
		      var argsIndex = -1,
		          argsLength = args.length,
		          holdersLength = holders.length,
		          leftIndex = -1,
		          leftLength = partials.length,
		          rangeLength = nativeMax(argsLength - holdersLength, 0),
		          result = Array(leftLength + rangeLength),
		          isUncurried = !isCurried;

		      while (++leftIndex < leftLength) {
		        result[leftIndex] = partials[leftIndex];
		      }
		      while (++argsIndex < holdersLength) {
		        if (isUncurried || argsIndex < argsLength) {
		          result[holders[argsIndex]] = args[argsIndex];
		        }
		      }
		      while (rangeLength--) {
		        result[leftIndex++] = args[argsIndex++];
		      }
		      return result;
		    }

		    /**
		     * This function is like `composeArgs` except that the arguments composition
		     * is tailored for `_.partialRight`.
		     *
		     * @private
		     * @param {Array} args The provided arguments.
		     * @param {Array} partials The arguments to append to those provided.
		     * @param {Array} holders The `partials` placeholder indexes.
		     * @params {boolean} [isCurried] Specify composing for a curried function.
		     * @returns {Array} Returns the new array of composed arguments.
		     */
		    function composeArgsRight(args, partials, holders, isCurried) {
		      var argsIndex = -1,
		          argsLength = args.length,
		          holdersIndex = -1,
		          holdersLength = holders.length,
		          rightIndex = -1,
		          rightLength = partials.length,
		          rangeLength = nativeMax(argsLength - holdersLength, 0),
		          result = Array(rangeLength + rightLength),
		          isUncurried = !isCurried;

		      while (++argsIndex < rangeLength) {
		        result[argsIndex] = args[argsIndex];
		      }
		      var offset = argsIndex;
		      while (++rightIndex < rightLength) {
		        result[offset + rightIndex] = partials[rightIndex];
		      }
		      while (++holdersIndex < holdersLength) {
		        if (isUncurried || argsIndex < argsLength) {
		          result[offset + holders[holdersIndex]] = args[argsIndex++];
		        }
		      }
		      return result;
		    }

		    /**
		     * Copies the values of `source` to `array`.
		     *
		     * @private
		     * @param {Array} source The array to copy values from.
		     * @param {Array} [array=[]] The array to copy values to.
		     * @returns {Array} Returns `array`.
		     */
		    function copyArray(source, array) {
		      var index = -1,
		          length = source.length;

		      array || (array = Array(length));
		      while (++index < length) {
		        array[index] = source[index];
		      }
		      return array;
		    }

		    /**
		     * Copies properties of `source` to `object`.
		     *
		     * @private
		     * @param {Object} source The object to copy properties from.
		     * @param {Array} props The property identifiers to copy.
		     * @param {Object} [object={}] The object to copy properties to.
		     * @param {Function} [customizer] The function to customize copied values.
		     * @returns {Object} Returns `object`.
		     */
		    function copyObject(source, props, object, customizer) {
		      var isNew = !object;
		      object || (object = {});

		      var index = -1,
		          length = props.length;

		      while (++index < length) {
		        var key = props[index];

		        var newValue = customizer
		          ? customizer(object[key], source[key], key, object, source)
		          : undefined$1;

		        if (newValue === undefined$1) {
		          newValue = source[key];
		        }
		        if (isNew) {
		          baseAssignValue(object, key, newValue);
		        } else {
		          assignValue(object, key, newValue);
		        }
		      }
		      return object;
		    }

		    /**
		     * Copies own symbols of `source` to `object`.
		     *
		     * @private
		     * @param {Object} source The object to copy symbols from.
		     * @param {Object} [object={}] The object to copy symbols to.
		     * @returns {Object} Returns `object`.
		     */
		    function copySymbols(source, object) {
		      return copyObject(source, getSymbols(source), object);
		    }

		    /**
		     * Copies own and inherited symbols of `source` to `object`.
		     *
		     * @private
		     * @param {Object} source The object to copy symbols from.
		     * @param {Object} [object={}] The object to copy symbols to.
		     * @returns {Object} Returns `object`.
		     */
		    function copySymbolsIn(source, object) {
		      return copyObject(source, getSymbolsIn(source), object);
		    }

		    /**
		     * Creates a function like `_.groupBy`.
		     *
		     * @private
		     * @param {Function} setter The function to set accumulator values.
		     * @param {Function} [initializer] The accumulator object initializer.
		     * @returns {Function} Returns the new aggregator function.
		     */
		    function createAggregator(setter, initializer) {
		      return function(collection, iteratee) {
		        var func = isArray(collection) ? arrayAggregator : baseAggregator,
		            accumulator = initializer ? initializer() : {};

		        return func(collection, setter, getIteratee(iteratee, 2), accumulator);
		      };
		    }

		    /**
		     * Creates a function like `_.assign`.
		     *
		     * @private
		     * @param {Function} assigner The function to assign values.
		     * @returns {Function} Returns the new assigner function.
		     */
		    function createAssigner(assigner) {
		      return baseRest(function(object, sources) {
		        var index = -1,
		            length = sources.length,
		            customizer = length > 1 ? sources[length - 1] : undefined$1,
		            guard = length > 2 ? sources[2] : undefined$1;

		        customizer = (assigner.length > 3 && typeof customizer == 'function')
		          ? (length--, customizer)
		          : undefined$1;

		        if (guard && isIterateeCall(sources[0], sources[1], guard)) {
		          customizer = length < 3 ? undefined$1 : customizer;
		          length = 1;
		        }
		        object = Object(object);
		        while (++index < length) {
		          var source = sources[index];
		          if (source) {
		            assigner(object, source, index, customizer);
		          }
		        }
		        return object;
		      });
		    }

		    /**
		     * Creates a `baseEach` or `baseEachRight` function.
		     *
		     * @private
		     * @param {Function} eachFunc The function to iterate over a collection.
		     * @param {boolean} [fromRight] Specify iterating from right to left.
		     * @returns {Function} Returns the new base function.
		     */
		    function createBaseEach(eachFunc, fromRight) {
		      return function(collection, iteratee) {
		        if (collection == null) {
		          return collection;
		        }
		        if (!isArrayLike(collection)) {
		          return eachFunc(collection, iteratee);
		        }
		        var length = collection.length,
		            index = fromRight ? length : -1,
		            iterable = Object(collection);

		        while ((fromRight ? index-- : ++index < length)) {
		          if (iteratee(iterable[index], index, iterable) === false) {
		            break;
		          }
		        }
		        return collection;
		      };
		    }

		    /**
		     * Creates a base function for methods like `_.forIn` and `_.forOwn`.
		     *
		     * @private
		     * @param {boolean} [fromRight] Specify iterating from right to left.
		     * @returns {Function} Returns the new base function.
		     */
		    function createBaseFor(fromRight) {
		      return function(object, iteratee, keysFunc) {
		        var index = -1,
		            iterable = Object(object),
		            props = keysFunc(object),
		            length = props.length;

		        while (length--) {
		          var key = props[fromRight ? length : ++index];
		          if (iteratee(iterable[key], key, iterable) === false) {
		            break;
		          }
		        }
		        return object;
		      };
		    }

		    /**
		     * Creates a function that wraps `func` to invoke it with the optional `this`
		     * binding of `thisArg`.
		     *
		     * @private
		     * @param {Function} func The function to wrap.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @param {*} [thisArg] The `this` binding of `func`.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createBind(func, bitmask, thisArg) {
		      var isBind = bitmask & WRAP_BIND_FLAG,
		          Ctor = createCtor(func);

		      function wrapper() {
		        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
		        return fn.apply(isBind ? thisArg : this, arguments);
		      }
		      return wrapper;
		    }

		    /**
		     * Creates a function like `_.lowerFirst`.
		     *
		     * @private
		     * @param {string} methodName The name of the `String` case method to use.
		     * @returns {Function} Returns the new case function.
		     */
		    function createCaseFirst(methodName) {
		      return function(string) {
		        string = toString(string);

		        var strSymbols = hasUnicode(string)
		          ? stringToArray(string)
		          : undefined$1;

		        var chr = strSymbols
		          ? strSymbols[0]
		          : string.charAt(0);

		        var trailing = strSymbols
		          ? castSlice(strSymbols, 1).join('')
		          : string.slice(1);

		        return chr[methodName]() + trailing;
		      };
		    }

		    /**
		     * Creates a function like `_.camelCase`.
		     *
		     * @private
		     * @param {Function} callback The function to combine each word.
		     * @returns {Function} Returns the new compounder function.
		     */
		    function createCompounder(callback) {
		      return function(string) {
		        return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
		      };
		    }

		    /**
		     * Creates a function that produces an instance of `Ctor` regardless of
		     * whether it was invoked as part of a `new` expression or by `call` or `apply`.
		     *
		     * @private
		     * @param {Function} Ctor The constructor to wrap.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createCtor(Ctor) {
		      return function() {
		        // Use a `switch` statement to work with class constructors. See
		        // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
		        // for more details.
		        var args = arguments;
		        switch (args.length) {
		          case 0: return new Ctor;
		          case 1: return new Ctor(args[0]);
		          case 2: return new Ctor(args[0], args[1]);
		          case 3: return new Ctor(args[0], args[1], args[2]);
		          case 4: return new Ctor(args[0], args[1], args[2], args[3]);
		          case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
		          case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
		          case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
		        }
		        var thisBinding = baseCreate(Ctor.prototype),
		            result = Ctor.apply(thisBinding, args);

		        // Mimic the constructor's `return` behavior.
		        // See https://es5.github.io/#x13.2.2 for more details.
		        return isObject(result) ? result : thisBinding;
		      };
		    }

		    /**
		     * Creates a function that wraps `func` to enable currying.
		     *
		     * @private
		     * @param {Function} func The function to wrap.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @param {number} arity The arity of `func`.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createCurry(func, bitmask, arity) {
		      var Ctor = createCtor(func);

		      function wrapper() {
		        var length = arguments.length,
		            args = Array(length),
		            index = length,
		            placeholder = getHolder(wrapper);

		        while (index--) {
		          args[index] = arguments[index];
		        }
		        var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
		          ? []
		          : replaceHolders(args, placeholder);

		        length -= holders.length;
		        if (length < arity) {
		          return createRecurry(
		            func, bitmask, createHybrid, wrapper.placeholder, undefined$1,
		            args, holders, undefined$1, undefined$1, arity - length);
		        }
		        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
		        return apply(fn, this, args);
		      }
		      return wrapper;
		    }

		    /**
		     * Creates a `_.find` or `_.findLast` function.
		     *
		     * @private
		     * @param {Function} findIndexFunc The function to find the collection index.
		     * @returns {Function} Returns the new find function.
		     */
		    function createFind(findIndexFunc) {
		      return function(collection, predicate, fromIndex) {
		        var iterable = Object(collection);
		        if (!isArrayLike(collection)) {
		          var iteratee = getIteratee(predicate, 3);
		          collection = keys(collection);
		          predicate = function(key) { return iteratee(iterable[key], key, iterable); };
		        }
		        var index = findIndexFunc(collection, predicate, fromIndex);
		        return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined$1;
		      };
		    }

		    /**
		     * Creates a `_.flow` or `_.flowRight` function.
		     *
		     * @private
		     * @param {boolean} [fromRight] Specify iterating from right to left.
		     * @returns {Function} Returns the new flow function.
		     */
		    function createFlow(fromRight) {
		      return flatRest(function(funcs) {
		        var length = funcs.length,
		            index = length,
		            prereq = LodashWrapper.prototype.thru;

		        if (fromRight) {
		          funcs.reverse();
		        }
		        while (index--) {
		          var func = funcs[index];
		          if (typeof func != 'function') {
		            throw new TypeError(FUNC_ERROR_TEXT);
		          }
		          if (prereq && !wrapper && getFuncName(func) == 'wrapper') {
		            var wrapper = new LodashWrapper([], true);
		          }
		        }
		        index = wrapper ? index : length;
		        while (++index < length) {
		          func = funcs[index];

		          var funcName = getFuncName(func),
		              data = funcName == 'wrapper' ? getData(func) : undefined$1;

		          if (data && isLaziable(data[0]) &&
		                data[1] == (WRAP_ARY_FLAG | WRAP_CURRY_FLAG | WRAP_PARTIAL_FLAG | WRAP_REARG_FLAG) &&
		                !data[4].length && data[9] == 1
		              ) {
		            wrapper = wrapper[getFuncName(data[0])].apply(wrapper, data[3]);
		          } else {
		            wrapper = (func.length == 1 && isLaziable(func))
		              ? wrapper[funcName]()
		              : wrapper.thru(func);
		          }
		        }
		        return function() {
		          var args = arguments,
		              value = args[0];

		          if (wrapper && args.length == 1 && isArray(value)) {
		            return wrapper.plant(value).value();
		          }
		          var index = 0,
		              result = length ? funcs[index].apply(this, args) : value;

		          while (++index < length) {
		            result = funcs[index].call(this, result);
		          }
		          return result;
		        };
		      });
		    }

		    /**
		     * Creates a function that wraps `func` to invoke it with optional `this`
		     * binding of `thisArg`, partial application, and currying.
		     *
		     * @private
		     * @param {Function|string} func The function or method name to wrap.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @param {*} [thisArg] The `this` binding of `func`.
		     * @param {Array} [partials] The arguments to prepend to those provided to
		     *  the new function.
		     * @param {Array} [holders] The `partials` placeholder indexes.
		     * @param {Array} [partialsRight] The arguments to append to those provided
		     *  to the new function.
		     * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
		     * @param {Array} [argPos] The argument positions of the new function.
		     * @param {number} [ary] The arity cap of `func`.
		     * @param {number} [arity] The arity of `func`.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
		      var isAry = bitmask & WRAP_ARY_FLAG,
		          isBind = bitmask & WRAP_BIND_FLAG,
		          isBindKey = bitmask & WRAP_BIND_KEY_FLAG,
		          isCurried = bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG),
		          isFlip = bitmask & WRAP_FLIP_FLAG,
		          Ctor = isBindKey ? undefined$1 : createCtor(func);

		      function wrapper() {
		        var length = arguments.length,
		            args = Array(length),
		            index = length;

		        while (index--) {
		          args[index] = arguments[index];
		        }
		        if (isCurried) {
		          var placeholder = getHolder(wrapper),
		              holdersCount = countHolders(args, placeholder);
		        }
		        if (partials) {
		          args = composeArgs(args, partials, holders, isCurried);
		        }
		        if (partialsRight) {
		          args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
		        }
		        length -= holdersCount;
		        if (isCurried && length < arity) {
		          var newHolders = replaceHolders(args, placeholder);
		          return createRecurry(
		            func, bitmask, createHybrid, wrapper.placeholder, thisArg,
		            args, newHolders, argPos, ary, arity - length
		          );
		        }
		        var thisBinding = isBind ? thisArg : this,
		            fn = isBindKey ? thisBinding[func] : func;

		        length = args.length;
		        if (argPos) {
		          args = reorder(args, argPos);
		        } else if (isFlip && length > 1) {
		          args.reverse();
		        }
		        if (isAry && ary < length) {
		          args.length = ary;
		        }
		        if (this && this !== root && this instanceof wrapper) {
		          fn = Ctor || createCtor(fn);
		        }
		        return fn.apply(thisBinding, args);
		      }
		      return wrapper;
		    }

		    /**
		     * Creates a function like `_.invertBy`.
		     *
		     * @private
		     * @param {Function} setter The function to set accumulator values.
		     * @param {Function} toIteratee The function to resolve iteratees.
		     * @returns {Function} Returns the new inverter function.
		     */
		    function createInverter(setter, toIteratee) {
		      return function(object, iteratee) {
		        return baseInverter(object, setter, toIteratee(iteratee), {});
		      };
		    }

		    /**
		     * Creates a function that performs a mathematical operation on two values.
		     *
		     * @private
		     * @param {Function} operator The function to perform the operation.
		     * @param {number} [defaultValue] The value used for `undefined` arguments.
		     * @returns {Function} Returns the new mathematical operation function.
		     */
		    function createMathOperation(operator, defaultValue) {
		      return function(value, other) {
		        var result;
		        if (value === undefined$1 && other === undefined$1) {
		          return defaultValue;
		        }
		        if (value !== undefined$1) {
		          result = value;
		        }
		        if (other !== undefined$1) {
		          if (result === undefined$1) {
		            return other;
		          }
		          if (typeof value == 'string' || typeof other == 'string') {
		            value = baseToString(value);
		            other = baseToString(other);
		          } else {
		            value = baseToNumber(value);
		            other = baseToNumber(other);
		          }
		          result = operator(value, other);
		        }
		        return result;
		      };
		    }

		    /**
		     * Creates a function like `_.over`.
		     *
		     * @private
		     * @param {Function} arrayFunc The function to iterate over iteratees.
		     * @returns {Function} Returns the new over function.
		     */
		    function createOver(arrayFunc) {
		      return flatRest(function(iteratees) {
		        iteratees = arrayMap(iteratees, baseUnary(getIteratee()));
		        return baseRest(function(args) {
		          var thisArg = this;
		          return arrayFunc(iteratees, function(iteratee) {
		            return apply(iteratee, thisArg, args);
		          });
		        });
		      });
		    }

		    /**
		     * Creates the padding for `string` based on `length`. The `chars` string
		     * is truncated if the number of characters exceeds `length`.
		     *
		     * @private
		     * @param {number} length The padding length.
		     * @param {string} [chars=' '] The string used as padding.
		     * @returns {string} Returns the padding for `string`.
		     */
		    function createPadding(length, chars) {
		      chars = chars === undefined$1 ? ' ' : baseToString(chars);

		      var charsLength = chars.length;
		      if (charsLength < 2) {
		        return charsLength ? baseRepeat(chars, length) : chars;
		      }
		      var result = baseRepeat(chars, nativeCeil(length / stringSize(chars)));
		      return hasUnicode(chars)
		        ? castSlice(stringToArray(result), 0, length).join('')
		        : result.slice(0, length);
		    }

		    /**
		     * Creates a function that wraps `func` to invoke it with the `this` binding
		     * of `thisArg` and `partials` prepended to the arguments it receives.
		     *
		     * @private
		     * @param {Function} func The function to wrap.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @param {*} thisArg The `this` binding of `func`.
		     * @param {Array} partials The arguments to prepend to those provided to
		     *  the new function.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createPartial(func, bitmask, thisArg, partials) {
		      var isBind = bitmask & WRAP_BIND_FLAG,
		          Ctor = createCtor(func);

		      function wrapper() {
		        var argsIndex = -1,
		            argsLength = arguments.length,
		            leftIndex = -1,
		            leftLength = partials.length,
		            args = Array(leftLength + argsLength),
		            fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

		        while (++leftIndex < leftLength) {
		          args[leftIndex] = partials[leftIndex];
		        }
		        while (argsLength--) {
		          args[leftIndex++] = arguments[++argsIndex];
		        }
		        return apply(fn, isBind ? thisArg : this, args);
		      }
		      return wrapper;
		    }

		    /**
		     * Creates a `_.range` or `_.rangeRight` function.
		     *
		     * @private
		     * @param {boolean} [fromRight] Specify iterating from right to left.
		     * @returns {Function} Returns the new range function.
		     */
		    function createRange(fromRight) {
		      return function(start, end, step) {
		        if (step && typeof step != 'number' && isIterateeCall(start, end, step)) {
		          end = step = undefined$1;
		        }
		        // Ensure the sign of `-0` is preserved.
		        start = toFinite(start);
		        if (end === undefined$1) {
		          end = start;
		          start = 0;
		        } else {
		          end = toFinite(end);
		        }
		        step = step === undefined$1 ? (start < end ? 1 : -1) : toFinite(step);
		        return baseRange(start, end, step, fromRight);
		      };
		    }

		    /**
		     * Creates a function that performs a relational operation on two values.
		     *
		     * @private
		     * @param {Function} operator The function to perform the operation.
		     * @returns {Function} Returns the new relational operation function.
		     */
		    function createRelationalOperation(operator) {
		      return function(value, other) {
		        if (!(typeof value == 'string' && typeof other == 'string')) {
		          value = toNumber(value);
		          other = toNumber(other);
		        }
		        return operator(value, other);
		      };
		    }

		    /**
		     * Creates a function that wraps `func` to continue currying.
		     *
		     * @private
		     * @param {Function} func The function to wrap.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @param {Function} wrapFunc The function to create the `func` wrapper.
		     * @param {*} placeholder The placeholder value.
		     * @param {*} [thisArg] The `this` binding of `func`.
		     * @param {Array} [partials] The arguments to prepend to those provided to
		     *  the new function.
		     * @param {Array} [holders] The `partials` placeholder indexes.
		     * @param {Array} [argPos] The argument positions of the new function.
		     * @param {number} [ary] The arity cap of `func`.
		     * @param {number} [arity] The arity of `func`.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
		      var isCurry = bitmask & WRAP_CURRY_FLAG,
		          newHolders = isCurry ? holders : undefined$1,
		          newHoldersRight = isCurry ? undefined$1 : holders,
		          newPartials = isCurry ? partials : undefined$1,
		          newPartialsRight = isCurry ? undefined$1 : partials;

		      bitmask |= (isCurry ? WRAP_PARTIAL_FLAG : WRAP_PARTIAL_RIGHT_FLAG);
		      bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG : WRAP_PARTIAL_FLAG);

		      if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
		        bitmask &= ~(WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG);
		      }
		      var newData = [
		        func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
		        newHoldersRight, argPos, ary, arity
		      ];

		      var result = wrapFunc.apply(undefined$1, newData);
		      if (isLaziable(func)) {
		        setData(result, newData);
		      }
		      result.placeholder = placeholder;
		      return setWrapToString(result, func, bitmask);
		    }

		    /**
		     * Creates a function like `_.round`.
		     *
		     * @private
		     * @param {string} methodName The name of the `Math` method to use when rounding.
		     * @returns {Function} Returns the new round function.
		     */
		    function createRound(methodName) {
		      var func = Math[methodName];
		      return function(number, precision) {
		        number = toNumber(number);
		        precision = precision == null ? 0 : nativeMin(toInteger(precision), 292);
		        if (precision && nativeIsFinite(number)) {
		          // Shift with exponential notation to avoid floating-point issues.
		          // See [MDN](https://mdn.io/round#Examples) for more details.
		          var pair = (toString(number) + 'e').split('e'),
		              value = func(pair[0] + 'e' + (+pair[1] + precision));

		          pair = (toString(value) + 'e').split('e');
		          return +(pair[0] + 'e' + (+pair[1] - precision));
		        }
		        return func(number);
		      };
		    }

		    /**
		     * Creates a set object of `values`.
		     *
		     * @private
		     * @param {Array} values The values to add to the set.
		     * @returns {Object} Returns the new set.
		     */
		    var createSet = !(Set && (1 / setToArray(new Set([,-0]))[1]) == INFINITY) ? noop : function(values) {
		      return new Set(values);
		    };

		    /**
		     * Creates a `_.toPairs` or `_.toPairsIn` function.
		     *
		     * @private
		     * @param {Function} keysFunc The function to get the keys of a given object.
		     * @returns {Function} Returns the new pairs function.
		     */
		    function createToPairs(keysFunc) {
		      return function(object) {
		        var tag = getTag(object);
		        if (tag == mapTag) {
		          return mapToArray(object);
		        }
		        if (tag == setTag) {
		          return setToPairs(object);
		        }
		        return baseToPairs(object, keysFunc(object));
		      };
		    }

		    /**
		     * Creates a function that either curries or invokes `func` with optional
		     * `this` binding and partially applied arguments.
		     *
		     * @private
		     * @param {Function|string} func The function or method name to wrap.
		     * @param {number} bitmask The bitmask flags.
		     *    1 - `_.bind`
		     *    2 - `_.bindKey`
		     *    4 - `_.curry` or `_.curryRight` of a bound function
		     *    8 - `_.curry`
		     *   16 - `_.curryRight`
		     *   32 - `_.partial`
		     *   64 - `_.partialRight`
		     *  128 - `_.rearg`
		     *  256 - `_.ary`
		     *  512 - `_.flip`
		     * @param {*} [thisArg] The `this` binding of `func`.
		     * @param {Array} [partials] The arguments to be partially applied.
		     * @param {Array} [holders] The `partials` placeholder indexes.
		     * @param {Array} [argPos] The argument positions of the new function.
		     * @param {number} [ary] The arity cap of `func`.
		     * @param {number} [arity] The arity of `func`.
		     * @returns {Function} Returns the new wrapped function.
		     */
		    function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
		      var isBindKey = bitmask & WRAP_BIND_KEY_FLAG;
		      if (!isBindKey && typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      var length = partials ? partials.length : 0;
		      if (!length) {
		        bitmask &= ~(WRAP_PARTIAL_FLAG | WRAP_PARTIAL_RIGHT_FLAG);
		        partials = holders = undefined$1;
		      }
		      ary = ary === undefined$1 ? ary : nativeMax(toInteger(ary), 0);
		      arity = arity === undefined$1 ? arity : toInteger(arity);
		      length -= holders ? holders.length : 0;

		      if (bitmask & WRAP_PARTIAL_RIGHT_FLAG) {
		        var partialsRight = partials,
		            holdersRight = holders;

		        partials = holders = undefined$1;
		      }
		      var data = isBindKey ? undefined$1 : getData(func);

		      var newData = [
		        func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
		        argPos, ary, arity
		      ];

		      if (data) {
		        mergeData(newData, data);
		      }
		      func = newData[0];
		      bitmask = newData[1];
		      thisArg = newData[2];
		      partials = newData[3];
		      holders = newData[4];
		      arity = newData[9] = newData[9] === undefined$1
		        ? (isBindKey ? 0 : func.length)
		        : nativeMax(newData[9] - length, 0);

		      if (!arity && bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG)) {
		        bitmask &= ~(WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG);
		      }
		      if (!bitmask || bitmask == WRAP_BIND_FLAG) {
		        var result = createBind(func, bitmask, thisArg);
		      } else if (bitmask == WRAP_CURRY_FLAG || bitmask == WRAP_CURRY_RIGHT_FLAG) {
		        result = createCurry(func, bitmask, arity);
		      } else if ((bitmask == WRAP_PARTIAL_FLAG || bitmask == (WRAP_BIND_FLAG | WRAP_PARTIAL_FLAG)) && !holders.length) {
		        result = createPartial(func, bitmask, thisArg, partials);
		      } else {
		        result = createHybrid.apply(undefined$1, newData);
		      }
		      var setter = data ? baseSetData : setData;
		      return setWrapToString(setter(result, newData), func, bitmask);
		    }

		    /**
		     * Used by `_.defaults` to customize its `_.assignIn` use to assign properties
		     * of source objects to the destination object for all destination properties
		     * that resolve to `undefined`.
		     *
		     * @private
		     * @param {*} objValue The destination value.
		     * @param {*} srcValue The source value.
		     * @param {string} key The key of the property to assign.
		     * @param {Object} object The parent object of `objValue`.
		     * @returns {*} Returns the value to assign.
		     */
		    function customDefaultsAssignIn(objValue, srcValue, key, object) {
		      if (objValue === undefined$1 ||
		          (eq(objValue, objectProto[key]) && !hasOwnProperty.call(object, key))) {
		        return srcValue;
		      }
		      return objValue;
		    }

		    /**
		     * Used by `_.defaultsDeep` to customize its `_.merge` use to merge source
		     * objects into destination objects that are passed thru.
		     *
		     * @private
		     * @param {*} objValue The destination value.
		     * @param {*} srcValue The source value.
		     * @param {string} key The key of the property to merge.
		     * @param {Object} object The parent object of `objValue`.
		     * @param {Object} source The parent object of `srcValue`.
		     * @param {Object} [stack] Tracks traversed source values and their merged
		     *  counterparts.
		     * @returns {*} Returns the value to assign.
		     */
		    function customDefaultsMerge(objValue, srcValue, key, object, source, stack) {
		      if (isObject(objValue) && isObject(srcValue)) {
		        // Recursively merge objects and arrays (susceptible to call stack limits).
		        stack.set(srcValue, objValue);
		        baseMerge(objValue, srcValue, undefined$1, customDefaultsMerge, stack);
		        stack['delete'](srcValue);
		      }
		      return objValue;
		    }

		    /**
		     * Used by `_.omit` to customize its `_.cloneDeep` use to only clone plain
		     * objects.
		     *
		     * @private
		     * @param {*} value The value to inspect.
		     * @param {string} key The key of the property to inspect.
		     * @returns {*} Returns the uncloned value or `undefined` to defer cloning to `_.cloneDeep`.
		     */
		    function customOmitClone(value) {
		      return isPlainObject(value) ? undefined$1 : value;
		    }

		    /**
		     * A specialized version of `baseIsEqualDeep` for arrays with support for
		     * partial deep comparisons.
		     *
		     * @private
		     * @param {Array} array The array to compare.
		     * @param {Array} other The other array to compare.
		     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
		     * @param {Function} customizer The function to customize comparisons.
		     * @param {Function} equalFunc The function to determine equivalents of values.
		     * @param {Object} stack Tracks traversed `array` and `other` objects.
		     * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
		     */
		    function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
		      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
		          arrLength = array.length,
		          othLength = other.length;

		      if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
		        return false;
		      }
		      // Check that cyclic values are equal.
		      var arrStacked = stack.get(array);
		      var othStacked = stack.get(other);
		      if (arrStacked && othStacked) {
		        return arrStacked == other && othStacked == array;
		      }
		      var index = -1,
		          result = true,
		          seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new SetCache : undefined$1;

		      stack.set(array, other);
		      stack.set(other, array);

		      // Ignore non-index properties.
		      while (++index < arrLength) {
		        var arrValue = array[index],
		            othValue = other[index];

		        if (customizer) {
		          var compared = isPartial
		            ? customizer(othValue, arrValue, index, other, array, stack)
		            : customizer(arrValue, othValue, index, array, other, stack);
		        }
		        if (compared !== undefined$1) {
		          if (compared) {
		            continue;
		          }
		          result = false;
		          break;
		        }
		        // Recursively compare arrays (susceptible to call stack limits).
		        if (seen) {
		          if (!arraySome(other, function(othValue, othIndex) {
		                if (!cacheHas(seen, othIndex) &&
		                    (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
		                  return seen.push(othIndex);
		                }
		              })) {
		            result = false;
		            break;
		          }
		        } else if (!(
		              arrValue === othValue ||
		                equalFunc(arrValue, othValue, bitmask, customizer, stack)
		            )) {
		          result = false;
		          break;
		        }
		      }
		      stack['delete'](array);
		      stack['delete'](other);
		      return result;
		    }

		    /**
		     * A specialized version of `baseIsEqualDeep` for comparing objects of
		     * the same `toStringTag`.
		     *
		     * **Note:** This function only supports comparing values with tags of
		     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
		     *
		     * @private
		     * @param {Object} object The object to compare.
		     * @param {Object} other The other object to compare.
		     * @param {string} tag The `toStringTag` of the objects to compare.
		     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
		     * @param {Function} customizer The function to customize comparisons.
		     * @param {Function} equalFunc The function to determine equivalents of values.
		     * @param {Object} stack Tracks traversed `object` and `other` objects.
		     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
		     */
		    function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
		      switch (tag) {
		        case dataViewTag:
		          if ((object.byteLength != other.byteLength) ||
		              (object.byteOffset != other.byteOffset)) {
		            return false;
		          }
		          object = object.buffer;
		          other = other.buffer;

		        case arrayBufferTag:
		          if ((object.byteLength != other.byteLength) ||
		              !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
		            return false;
		          }
		          return true;

		        case boolTag:
		        case dateTag:
		        case numberTag:
		          // Coerce booleans to `1` or `0` and dates to milliseconds.
		          // Invalid dates are coerced to `NaN`.
		          return eq(+object, +other);

		        case errorTag:
		          return object.name == other.name && object.message == other.message;

		        case regexpTag:
		        case stringTag:
		          // Coerce regexes to strings and treat strings, primitives and objects,
		          // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
		          // for more details.
		          return object == (other + '');

		        case mapTag:
		          var convert = mapToArray;

		        case setTag:
		          var isPartial = bitmask & COMPARE_PARTIAL_FLAG;
		          convert || (convert = setToArray);

		          if (object.size != other.size && !isPartial) {
		            return false;
		          }
		          // Assume cyclic values are equal.
		          var stacked = stack.get(object);
		          if (stacked) {
		            return stacked == other;
		          }
		          bitmask |= COMPARE_UNORDERED_FLAG;

		          // Recursively compare objects (susceptible to call stack limits).
		          stack.set(object, other);
		          var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
		          stack['delete'](object);
		          return result;

		        case symbolTag:
		          if (symbolValueOf) {
		            return symbolValueOf.call(object) == symbolValueOf.call(other);
		          }
		      }
		      return false;
		    }

		    /**
		     * A specialized version of `baseIsEqualDeep` for objects with support for
		     * partial deep comparisons.
		     *
		     * @private
		     * @param {Object} object The object to compare.
		     * @param {Object} other The other object to compare.
		     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
		     * @param {Function} customizer The function to customize comparisons.
		     * @param {Function} equalFunc The function to determine equivalents of values.
		     * @param {Object} stack Tracks traversed `object` and `other` objects.
		     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
		     */
		    function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
		      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
		          objProps = getAllKeys(object),
		          objLength = objProps.length,
		          othProps = getAllKeys(other),
		          othLength = othProps.length;

		      if (objLength != othLength && !isPartial) {
		        return false;
		      }
		      var index = objLength;
		      while (index--) {
		        var key = objProps[index];
		        if (!(isPartial ? key in other : hasOwnProperty.call(other, key))) {
		          return false;
		        }
		      }
		      // Check that cyclic values are equal.
		      var objStacked = stack.get(object);
		      var othStacked = stack.get(other);
		      if (objStacked && othStacked) {
		        return objStacked == other && othStacked == object;
		      }
		      var result = true;
		      stack.set(object, other);
		      stack.set(other, object);

		      var skipCtor = isPartial;
		      while (++index < objLength) {
		        key = objProps[index];
		        var objValue = object[key],
		            othValue = other[key];

		        if (customizer) {
		          var compared = isPartial
		            ? customizer(othValue, objValue, key, other, object, stack)
		            : customizer(objValue, othValue, key, object, other, stack);
		        }
		        // Recursively compare objects (susceptible to call stack limits).
		        if (!(compared === undefined$1
		              ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
		              : compared
		            )) {
		          result = false;
		          break;
		        }
		        skipCtor || (skipCtor = key == 'constructor');
		      }
		      if (result && !skipCtor) {
		        var objCtor = object.constructor,
		            othCtor = other.constructor;

		        // Non `Object` object instances with different constructors are not equal.
		        if (objCtor != othCtor &&
		            ('constructor' in object && 'constructor' in other) &&
		            !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
		              typeof othCtor == 'function' && othCtor instanceof othCtor)) {
		          result = false;
		        }
		      }
		      stack['delete'](object);
		      stack['delete'](other);
		      return result;
		    }

		    /**
		     * A specialized version of `baseRest` which flattens the rest array.
		     *
		     * @private
		     * @param {Function} func The function to apply a rest parameter to.
		     * @returns {Function} Returns the new function.
		     */
		    function flatRest(func) {
		      return setToString(overRest(func, undefined$1, flatten), func + '');
		    }

		    /**
		     * Creates an array of own enumerable property names and symbols of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names and symbols.
		     */
		    function getAllKeys(object) {
		      return baseGetAllKeys(object, keys, getSymbols);
		    }

		    /**
		     * Creates an array of own and inherited enumerable property names and
		     * symbols of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names and symbols.
		     */
		    function getAllKeysIn(object) {
		      return baseGetAllKeys(object, keysIn, getSymbolsIn);
		    }

		    /**
		     * Gets metadata for `func`.
		     *
		     * @private
		     * @param {Function} func The function to query.
		     * @returns {*} Returns the metadata for `func`.
		     */
		    var getData = !metaMap ? noop : function(func) {
		      return metaMap.get(func);
		    };

		    /**
		     * Gets the name of `func`.
		     *
		     * @private
		     * @param {Function} func The function to query.
		     * @returns {string} Returns the function name.
		     */
		    function getFuncName(func) {
		      var result = (func.name + ''),
		          array = realNames[result],
		          length = hasOwnProperty.call(realNames, result) ? array.length : 0;

		      while (length--) {
		        var data = array[length],
		            otherFunc = data.func;
		        if (otherFunc == null || otherFunc == func) {
		          return data.name;
		        }
		      }
		      return result;
		    }

		    /**
		     * Gets the argument placeholder value for `func`.
		     *
		     * @private
		     * @param {Function} func The function to inspect.
		     * @returns {*} Returns the placeholder value.
		     */
		    function getHolder(func) {
		      var object = hasOwnProperty.call(lodash, 'placeholder') ? lodash : func;
		      return object.placeholder;
		    }

		    /**
		     * Gets the appropriate "iteratee" function. If `_.iteratee` is customized,
		     * this function returns the custom method, otherwise it returns `baseIteratee`.
		     * If arguments are provided, the chosen function is invoked with them and
		     * its result is returned.
		     *
		     * @private
		     * @param {*} [value] The value to convert to an iteratee.
		     * @param {number} [arity] The arity of the created iteratee.
		     * @returns {Function} Returns the chosen function or its result.
		     */
		    function getIteratee() {
		      var result = lodash.iteratee || iteratee;
		      result = result === iteratee ? baseIteratee : result;
		      return arguments.length ? result(arguments[0], arguments[1]) : result;
		    }

		    /**
		     * Gets the data for `map`.
		     *
		     * @private
		     * @param {Object} map The map to query.
		     * @param {string} key The reference key.
		     * @returns {*} Returns the map data.
		     */
		    function getMapData(map, key) {
		      var data = map.__data__;
		      return isKeyable(key)
		        ? data[typeof key == 'string' ? 'string' : 'hash']
		        : data.map;
		    }

		    /**
		     * Gets the property names, values, and compare flags of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the match data of `object`.
		     */
		    function getMatchData(object) {
		      var result = keys(object),
		          length = result.length;

		      while (length--) {
		        var key = result[length],
		            value = object[key];

		        result[length] = [key, value, isStrictComparable(value)];
		      }
		      return result;
		    }

		    /**
		     * Gets the native function at `key` of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {string} key The key of the method to get.
		     * @returns {*} Returns the function if it's native, else `undefined`.
		     */
		    function getNative(object, key) {
		      var value = getValue(object, key);
		      return baseIsNative(value) ? value : undefined$1;
		    }

		    /**
		     * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
		     *
		     * @private
		     * @param {*} value The value to query.
		     * @returns {string} Returns the raw `toStringTag`.
		     */
		    function getRawTag(value) {
		      var isOwn = hasOwnProperty.call(value, symToStringTag),
		          tag = value[symToStringTag];

		      try {
		        value[symToStringTag] = undefined$1;
		        var unmasked = true;
		      } catch (e) {}

		      var result = nativeObjectToString.call(value);
		      if (unmasked) {
		        if (isOwn) {
		          value[symToStringTag] = tag;
		        } else {
		          delete value[symToStringTag];
		        }
		      }
		      return result;
		    }

		    /**
		     * Creates an array of the own enumerable symbols of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of symbols.
		     */
		    var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
		      if (object == null) {
		        return [];
		      }
		      object = Object(object);
		      return arrayFilter(nativeGetSymbols(object), function(symbol) {
		        return propertyIsEnumerable.call(object, symbol);
		      });
		    };

		    /**
		     * Creates an array of the own and inherited enumerable symbols of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of symbols.
		     */
		    var getSymbolsIn = !nativeGetSymbols ? stubArray : function(object) {
		      var result = [];
		      while (object) {
		        arrayPush(result, getSymbols(object));
		        object = getPrototype(object);
		      }
		      return result;
		    };

		    /**
		     * Gets the `toStringTag` of `value`.
		     *
		     * @private
		     * @param {*} value The value to query.
		     * @returns {string} Returns the `toStringTag`.
		     */
		    var getTag = baseGetTag;

		    // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
		    if ((DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag) ||
		        (Map && getTag(new Map) != mapTag) ||
		        (Promise && getTag(Promise.resolve()) != promiseTag) ||
		        (Set && getTag(new Set) != setTag) ||
		        (WeakMap && getTag(new WeakMap) != weakMapTag)) {
		      getTag = function(value) {
		        var result = baseGetTag(value),
		            Ctor = result == objectTag ? value.constructor : undefined$1,
		            ctorString = Ctor ? toSource(Ctor) : '';

		        if (ctorString) {
		          switch (ctorString) {
		            case dataViewCtorString: return dataViewTag;
		            case mapCtorString: return mapTag;
		            case promiseCtorString: return promiseTag;
		            case setCtorString: return setTag;
		            case weakMapCtorString: return weakMapTag;
		          }
		        }
		        return result;
		      };
		    }

		    /**
		     * Gets the view, applying any `transforms` to the `start` and `end` positions.
		     *
		     * @private
		     * @param {number} start The start of the view.
		     * @param {number} end The end of the view.
		     * @param {Array} transforms The transformations to apply to the view.
		     * @returns {Object} Returns an object containing the `start` and `end`
		     *  positions of the view.
		     */
		    function getView(start, end, transforms) {
		      var index = -1,
		          length = transforms.length;

		      while (++index < length) {
		        var data = transforms[index],
		            size = data.size;

		        switch (data.type) {
		          case 'drop':      start += size; break;
		          case 'dropRight': end -= size; break;
		          case 'take':      end = nativeMin(end, start + size); break;
		          case 'takeRight': start = nativeMax(start, end - size); break;
		        }
		      }
		      return { 'start': start, 'end': end };
		    }

		    /**
		     * Extracts wrapper details from the `source` body comment.
		     *
		     * @private
		     * @param {string} source The source to inspect.
		     * @returns {Array} Returns the wrapper details.
		     */
		    function getWrapDetails(source) {
		      var match = source.match(reWrapDetails);
		      return match ? match[1].split(reSplitDetails) : [];
		    }

		    /**
		     * Checks if `path` exists on `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path to check.
		     * @param {Function} hasFunc The function to check properties.
		     * @returns {boolean} Returns `true` if `path` exists, else `false`.
		     */
		    function hasPath(object, path, hasFunc) {
		      path = castPath(path, object);

		      var index = -1,
		          length = path.length,
		          result = false;

		      while (++index < length) {
		        var key = toKey(path[index]);
		        if (!(result = object != null && hasFunc(object, key))) {
		          break;
		        }
		        object = object[key];
		      }
		      if (result || ++index != length) {
		        return result;
		      }
		      length = object == null ? 0 : object.length;
		      return !!length && isLength(length) && isIndex(key, length) &&
		        (isArray(object) || isArguments(object));
		    }

		    /**
		     * Initializes an array clone.
		     *
		     * @private
		     * @param {Array} array The array to clone.
		     * @returns {Array} Returns the initialized clone.
		     */
		    function initCloneArray(array) {
		      var length = array.length,
		          result = new array.constructor(length);

		      // Add properties assigned by `RegExp#exec`.
		      if (length && typeof array[0] == 'string' && hasOwnProperty.call(array, 'index')) {
		        result.index = array.index;
		        result.input = array.input;
		      }
		      return result;
		    }

		    /**
		     * Initializes an object clone.
		     *
		     * @private
		     * @param {Object} object The object to clone.
		     * @returns {Object} Returns the initialized clone.
		     */
		    function initCloneObject(object) {
		      return (typeof object.constructor == 'function' && !isPrototype(object))
		        ? baseCreate(getPrototype(object))
		        : {};
		    }

		    /**
		     * Initializes an object clone based on its `toStringTag`.
		     *
		     * **Note:** This function only supports cloning values with tags of
		     * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
		     *
		     * @private
		     * @param {Object} object The object to clone.
		     * @param {string} tag The `toStringTag` of the object to clone.
		     * @param {boolean} [isDeep] Specify a deep clone.
		     * @returns {Object} Returns the initialized clone.
		     */
		    function initCloneByTag(object, tag, isDeep) {
		      var Ctor = object.constructor;
		      switch (tag) {
		        case arrayBufferTag:
		          return cloneArrayBuffer(object);

		        case boolTag:
		        case dateTag:
		          return new Ctor(+object);

		        case dataViewTag:
		          return cloneDataView(object, isDeep);

		        case float32Tag: case float64Tag:
		        case int8Tag: case int16Tag: case int32Tag:
		        case uint8Tag: case uint8ClampedTag: case uint16Tag: case uint32Tag:
		          return cloneTypedArray(object, isDeep);

		        case mapTag:
		          return new Ctor;

		        case numberTag:
		        case stringTag:
		          return new Ctor(object);

		        case regexpTag:
		          return cloneRegExp(object);

		        case setTag:
		          return new Ctor;

		        case symbolTag:
		          return cloneSymbol(object);
		      }
		    }

		    /**
		     * Inserts wrapper `details` in a comment at the top of the `source` body.
		     *
		     * @private
		     * @param {string} source The source to modify.
		     * @returns {Array} details The details to insert.
		     * @returns {string} Returns the modified source.
		     */
		    function insertWrapDetails(source, details) {
		      var length = details.length;
		      if (!length) {
		        return source;
		      }
		      var lastIndex = length - 1;
		      details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
		      details = details.join(length > 2 ? ', ' : ' ');
		      return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
		    }

		    /**
		     * Checks if `value` is a flattenable `arguments` object or array.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
		     */
		    function isFlattenable(value) {
		      return isArray(value) || isArguments(value) ||
		        !!(spreadableSymbol && value && value[spreadableSymbol]);
		    }

		    /**
		     * Checks if `value` is a valid array-like index.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
		     * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
		     */
		    function isIndex(value, length) {
		      var type = typeof value;
		      length = length == null ? MAX_SAFE_INTEGER : length;

		      return !!length &&
		        (type == 'number' ||
		          (type != 'symbol' && reIsUint.test(value))) &&
		            (value > -1 && value % 1 == 0 && value < length);
		    }

		    /**
		     * Checks if the given arguments are from an iteratee call.
		     *
		     * @private
		     * @param {*} value The potential iteratee value argument.
		     * @param {*} index The potential iteratee index or key argument.
		     * @param {*} object The potential iteratee object argument.
		     * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
		     *  else `false`.
		     */
		    function isIterateeCall(value, index, object) {
		      if (!isObject(object)) {
		        return false;
		      }
		      var type = typeof index;
		      if (type == 'number'
		            ? (isArrayLike(object) && isIndex(index, object.length))
		            : (type == 'string' && index in object)
		          ) {
		        return eq(object[index], value);
		      }
		      return false;
		    }

		    /**
		     * Checks if `value` is a property name and not a property path.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @param {Object} [object] The object to query keys on.
		     * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
		     */
		    function isKey(value, object) {
		      if (isArray(value)) {
		        return false;
		      }
		      var type = typeof value;
		      if (type == 'number' || type == 'symbol' || type == 'boolean' ||
		          value == null || isSymbol(value)) {
		        return true;
		      }
		      return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
		        (object != null && value in Object(object));
		    }

		    /**
		     * Checks if `value` is suitable for use as unique object key.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
		     */
		    function isKeyable(value) {
		      var type = typeof value;
		      return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
		        ? (value !== '__proto__')
		        : (value === null);
		    }

		    /**
		     * Checks if `func` has a lazy counterpart.
		     *
		     * @private
		     * @param {Function} func The function to check.
		     * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
		     *  else `false`.
		     */
		    function isLaziable(func) {
		      var funcName = getFuncName(func),
		          other = lodash[funcName];

		      if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
		        return false;
		      }
		      if (func === other) {
		        return true;
		      }
		      var data = getData(other);
		      return !!data && func === data[0];
		    }

		    /**
		     * Checks if `func` has its source masked.
		     *
		     * @private
		     * @param {Function} func The function to check.
		     * @returns {boolean} Returns `true` if `func` is masked, else `false`.
		     */
		    function isMasked(func) {
		      return !!maskSrcKey && (maskSrcKey in func);
		    }

		    /**
		     * Checks if `func` is capable of being masked.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `func` is maskable, else `false`.
		     */
		    var isMaskable = coreJsData ? isFunction : stubFalse;

		    /**
		     * Checks if `value` is likely a prototype object.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
		     */
		    function isPrototype(value) {
		      var Ctor = value && value.constructor,
		          proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

		      return value === proto;
		    }

		    /**
		     * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
		     *
		     * @private
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` if suitable for strict
		     *  equality comparisons, else `false`.
		     */
		    function isStrictComparable(value) {
		      return value === value && !isObject(value);
		    }

		    /**
		     * A specialized version of `matchesProperty` for source values suitable
		     * for strict equality comparisons, i.e. `===`.
		     *
		     * @private
		     * @param {string} key The key of the property to get.
		     * @param {*} srcValue The value to match.
		     * @returns {Function} Returns the new spec function.
		     */
		    function matchesStrictComparable(key, srcValue) {
		      return function(object) {
		        if (object == null) {
		          return false;
		        }
		        return object[key] === srcValue &&
		          (srcValue !== undefined$1 || (key in Object(object)));
		      };
		    }

		    /**
		     * A specialized version of `_.memoize` which clears the memoized function's
		     * cache when it exceeds `MAX_MEMOIZE_SIZE`.
		     *
		     * @private
		     * @param {Function} func The function to have its output memoized.
		     * @returns {Function} Returns the new memoized function.
		     */
		    function memoizeCapped(func) {
		      var result = memoize(func, function(key) {
		        if (cache.size === MAX_MEMOIZE_SIZE) {
		          cache.clear();
		        }
		        return key;
		      });

		      var cache = result.cache;
		      return result;
		    }

		    /**
		     * Merges the function metadata of `source` into `data`.
		     *
		     * Merging metadata reduces the number of wrappers used to invoke a function.
		     * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
		     * may be applied regardless of execution order. Methods like `_.ary` and
		     * `_.rearg` modify function arguments, making the order in which they are
		     * executed important, preventing the merging of metadata. However, we make
		     * an exception for a safe combined case where curried functions have `_.ary`
		     * and or `_.rearg` applied.
		     *
		     * @private
		     * @param {Array} data The destination metadata.
		     * @param {Array} source The source metadata.
		     * @returns {Array} Returns `data`.
		     */
		    function mergeData(data, source) {
		      var bitmask = data[1],
		          srcBitmask = source[1],
		          newBitmask = bitmask | srcBitmask,
		          isCommon = newBitmask < (WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG | WRAP_ARY_FLAG);

		      var isCombo =
		        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_CURRY_FLAG)) ||
		        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_REARG_FLAG) && (data[7].length <= source[8])) ||
		        ((srcBitmask == (WRAP_ARY_FLAG | WRAP_REARG_FLAG)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG));

		      // Exit early if metadata can't be merged.
		      if (!(isCommon || isCombo)) {
		        return data;
		      }
		      // Use source `thisArg` if available.
		      if (srcBitmask & WRAP_BIND_FLAG) {
		        data[2] = source[2];
		        // Set when currying a bound function.
		        newBitmask |= bitmask & WRAP_BIND_FLAG ? 0 : WRAP_CURRY_BOUND_FLAG;
		      }
		      // Compose partial arguments.
		      var value = source[3];
		      if (value) {
		        var partials = data[3];
		        data[3] = partials ? composeArgs(partials, value, source[4]) : value;
		        data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
		      }
		      // Compose partial right arguments.
		      value = source[5];
		      if (value) {
		        partials = data[5];
		        data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
		        data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
		      }
		      // Use source `argPos` if available.
		      value = source[7];
		      if (value) {
		        data[7] = value;
		      }
		      // Use source `ary` if it's smaller.
		      if (srcBitmask & WRAP_ARY_FLAG) {
		        data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
		      }
		      // Use source `arity` if one is not provided.
		      if (data[9] == null) {
		        data[9] = source[9];
		      }
		      // Use source `func` and merge bitmasks.
		      data[0] = source[0];
		      data[1] = newBitmask;

		      return data;
		    }

		    /**
		     * This function is like
		     * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
		     * except that it includes inherited enumerable properties.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names.
		     */
		    function nativeKeysIn(object) {
		      var result = [];
		      if (object != null) {
		        for (var key in Object(object)) {
		          result.push(key);
		        }
		      }
		      return result;
		    }

		    /**
		     * Converts `value` to a string using `Object.prototype.toString`.
		     *
		     * @private
		     * @param {*} value The value to convert.
		     * @returns {string} Returns the converted string.
		     */
		    function objectToString(value) {
		      return nativeObjectToString.call(value);
		    }

		    /**
		     * A specialized version of `baseRest` which transforms the rest array.
		     *
		     * @private
		     * @param {Function} func The function to apply a rest parameter to.
		     * @param {number} [start=func.length-1] The start position of the rest parameter.
		     * @param {Function} transform The rest array transform.
		     * @returns {Function} Returns the new function.
		     */
		    function overRest(func, start, transform) {
		      start = nativeMax(start === undefined$1 ? (func.length - 1) : start, 0);
		      return function() {
		        var args = arguments,
		            index = -1,
		            length = nativeMax(args.length - start, 0),
		            array = Array(length);

		        while (++index < length) {
		          array[index] = args[start + index];
		        }
		        index = -1;
		        var otherArgs = Array(start + 1);
		        while (++index < start) {
		          otherArgs[index] = args[index];
		        }
		        otherArgs[start] = transform(array);
		        return apply(func, this, otherArgs);
		      };
		    }

		    /**
		     * Gets the parent value at `path` of `object`.
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {Array} path The path to get the parent value of.
		     * @returns {*} Returns the parent value.
		     */
		    function parent(object, path) {
		      return path.length < 2 ? object : baseGet(object, baseSlice(path, 0, -1));
		    }

		    /**
		     * Reorder `array` according to the specified indexes where the element at
		     * the first index is assigned as the first element, the element at
		     * the second index is assigned as the second element, and so on.
		     *
		     * @private
		     * @param {Array} array The array to reorder.
		     * @param {Array} indexes The arranged array indexes.
		     * @returns {Array} Returns `array`.
		     */
		    function reorder(array, indexes) {
		      var arrLength = array.length,
		          length = nativeMin(indexes.length, arrLength),
		          oldArray = copyArray(array);

		      while (length--) {
		        var index = indexes[length];
		        array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined$1;
		      }
		      return array;
		    }

		    /**
		     * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
		     *
		     * @private
		     * @param {Object} object The object to query.
		     * @param {string} key The key of the property to get.
		     * @returns {*} Returns the property value.
		     */
		    function safeGet(object, key) {
		      if (key === 'constructor' && typeof object[key] === 'function') {
		        return;
		      }

		      if (key == '__proto__') {
		        return;
		      }

		      return object[key];
		    }

		    /**
		     * Sets metadata for `func`.
		     *
		     * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
		     * period of time, it will trip its breaker and transition to an identity
		     * function to avoid garbage collection pauses in V8. See
		     * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
		     * for more details.
		     *
		     * @private
		     * @param {Function} func The function to associate metadata with.
		     * @param {*} data The metadata.
		     * @returns {Function} Returns `func`.
		     */
		    var setData = shortOut(baseSetData);

		    /**
		     * A simple wrapper around the global [`setTimeout`](https://mdn.io/setTimeout).
		     *
		     * @private
		     * @param {Function} func The function to delay.
		     * @param {number} wait The number of milliseconds to delay invocation.
		     * @returns {number|Object} Returns the timer id or timeout object.
		     */
		    var setTimeout = ctxSetTimeout || function(func, wait) {
		      return root.setTimeout(func, wait);
		    };

		    /**
		     * Sets the `toString` method of `func` to return `string`.
		     *
		     * @private
		     * @param {Function} func The function to modify.
		     * @param {Function} string The `toString` result.
		     * @returns {Function} Returns `func`.
		     */
		    var setToString = shortOut(baseSetToString);

		    /**
		     * Sets the `toString` method of `wrapper` to mimic the source of `reference`
		     * with wrapper details in a comment at the top of the source body.
		     *
		     * @private
		     * @param {Function} wrapper The function to modify.
		     * @param {Function} reference The reference function.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @returns {Function} Returns `wrapper`.
		     */
		    function setWrapToString(wrapper, reference, bitmask) {
		      var source = (reference + '');
		      return setToString(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
		    }

		    /**
		     * Creates a function that'll short out and invoke `identity` instead
		     * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
		     * milliseconds.
		     *
		     * @private
		     * @param {Function} func The function to restrict.
		     * @returns {Function} Returns the new shortable function.
		     */
		    function shortOut(func) {
		      var count = 0,
		          lastCalled = 0;

		      return function() {
		        var stamp = nativeNow(),
		            remaining = HOT_SPAN - (stamp - lastCalled);

		        lastCalled = stamp;
		        if (remaining > 0) {
		          if (++count >= HOT_COUNT) {
		            return arguments[0];
		          }
		        } else {
		          count = 0;
		        }
		        return func.apply(undefined$1, arguments);
		      };
		    }

		    /**
		     * A specialized version of `_.shuffle` which mutates and sets the size of `array`.
		     *
		     * @private
		     * @param {Array} array The array to shuffle.
		     * @param {number} [size=array.length] The size of `array`.
		     * @returns {Array} Returns `array`.
		     */
		    function shuffleSelf(array, size) {
		      var index = -1,
		          length = array.length,
		          lastIndex = length - 1;

		      size = size === undefined$1 ? length : size;
		      while (++index < size) {
		        var rand = baseRandom(index, lastIndex),
		            value = array[rand];

		        array[rand] = array[index];
		        array[index] = value;
		      }
		      array.length = size;
		      return array;
		    }

		    /**
		     * Converts `string` to a property path array.
		     *
		     * @private
		     * @param {string} string The string to convert.
		     * @returns {Array} Returns the property path array.
		     */
		    var stringToPath = memoizeCapped(function(string) {
		      var result = [];
		      if (string.charCodeAt(0) === 46 /* . */) {
		        result.push('');
		      }
		      string.replace(rePropName, function(match, number, quote, subString) {
		        result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
		      });
		      return result;
		    });

		    /**
		     * Converts `value` to a string key if it's not a string or symbol.
		     *
		     * @private
		     * @param {*} value The value to inspect.
		     * @returns {string|symbol} Returns the key.
		     */
		    function toKey(value) {
		      if (typeof value == 'string' || isSymbol(value)) {
		        return value;
		      }
		      var result = (value + '');
		      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
		    }

		    /**
		     * Converts `func` to its source code.
		     *
		     * @private
		     * @param {Function} func The function to convert.
		     * @returns {string} Returns the source code.
		     */
		    function toSource(func) {
		      if (func != null) {
		        try {
		          return funcToString.call(func);
		        } catch (e) {}
		        try {
		          return (func + '');
		        } catch (e) {}
		      }
		      return '';
		    }

		    /**
		     * Updates wrapper `details` based on `bitmask` flags.
		     *
		     * @private
		     * @returns {Array} details The details to modify.
		     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
		     * @returns {Array} Returns `details`.
		     */
		    function updateWrapDetails(details, bitmask) {
		      arrayEach(wrapFlags, function(pair) {
		        var value = '_.' + pair[0];
		        if ((bitmask & pair[1]) && !arrayIncludes(details, value)) {
		          details.push(value);
		        }
		      });
		      return details.sort();
		    }

		    /**
		     * Creates a clone of `wrapper`.
		     *
		     * @private
		     * @param {Object} wrapper The wrapper to clone.
		     * @returns {Object} Returns the cloned wrapper.
		     */
		    function wrapperClone(wrapper) {
		      if (wrapper instanceof LazyWrapper) {
		        return wrapper.clone();
		      }
		      var result = new LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
		      result.__actions__ = copyArray(wrapper.__actions__);
		      result.__index__  = wrapper.__index__;
		      result.__values__ = wrapper.__values__;
		      return result;
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates an array of elements split into groups the length of `size`.
		     * If `array` can't be split evenly, the final chunk will be the remaining
		     * elements.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to process.
		     * @param {number} [size=1] The length of each chunk
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the new array of chunks.
		     * @example
		     *
		     * _.chunk(['a', 'b', 'c', 'd'], 2);
		     * // => [['a', 'b'], ['c', 'd']]
		     *
		     * _.chunk(['a', 'b', 'c', 'd'], 3);
		     * // => [['a', 'b', 'c'], ['d']]
		     */
		    function chunk(array, size, guard) {
		      if ((guard ? isIterateeCall(array, size, guard) : size === undefined$1)) {
		        size = 1;
		      } else {
		        size = nativeMax(toInteger(size), 0);
		      }
		      var length = array == null ? 0 : array.length;
		      if (!length || size < 1) {
		        return [];
		      }
		      var index = 0,
		          resIndex = 0,
		          result = Array(nativeCeil(length / size));

		      while (index < length) {
		        result[resIndex++] = baseSlice(array, index, (index += size));
		      }
		      return result;
		    }

		    /**
		     * Creates an array with all falsey values removed. The values `false`, `null`,
		     * `0`, `-0`, `0n`, `""`, `undefined`, and `NaN` are falsy.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to compact.
		     * @returns {Array} Returns the new array of filtered values.
		     * @example
		     *
		     * _.compact([0, 1, false, 2, '', 3]);
		     * // => [1, 2, 3]
		     */
		    function compact(array) {
		      var index = -1,
		          length = array == null ? 0 : array.length,
		          resIndex = 0,
		          result = [];

		      while (++index < length) {
		        var value = array[index];
		        if (value) {
		          result[resIndex++] = value;
		        }
		      }
		      return result;
		    }

		    /**
		     * Creates a new array concatenating `array` with any additional arrays
		     * and/or values.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to concatenate.
		     * @param {...*} [values] The values to concatenate.
		     * @returns {Array} Returns the new concatenated array.
		     * @example
		     *
		     * var array = [1];
		     * var other = _.concat(array, 2, [3], [[4]]);
		     *
		     * console.log(other);
		     * // => [1, 2, 3, [4]]
		     *
		     * console.log(array);
		     * // => [1]
		     */
		    function concat() {
		      var length = arguments.length;
		      if (!length) {
		        return [];
		      }
		      var args = Array(length - 1),
		          array = arguments[0],
		          index = length;

		      while (index--) {
		        args[index - 1] = arguments[index];
		      }
		      return arrayPush(isArray(array) ? copyArray(array) : [array], baseFlatten(args, 1));
		    }

		    /**
		     * Creates an array of `array` values not included in the other given arrays
		     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons. The order and references of result values are
		     * determined by the first array.
		     *
		     * **Note:** Unlike `_.pullAll`, this method returns a new array.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {...Array} [values] The values to exclude.
		     * @returns {Array} Returns the new array of filtered values.
		     * @see _.without, _.xor
		     * @example
		     *
		     * _.difference([2, 1], [2, 3]);
		     * // => [1]
		     */
		    var difference = baseRest(function(array, values) {
		      return isArrayLikeObject(array)
		        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true))
		        : [];
		    });

		    /**
		     * This method is like `_.difference` except that it accepts `iteratee` which
		     * is invoked for each element of `array` and `values` to generate the criterion
		     * by which they're compared. The order and references of result values are
		     * determined by the first array. The iteratee is invoked with one argument:
		     * (value).
		     *
		     * **Note:** Unlike `_.pullAllBy`, this method returns a new array.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {...Array} [values] The values to exclude.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Array} Returns the new array of filtered values.
		     * @example
		     *
		     * _.differenceBy([2.1, 1.2], [2.3, 3.4], Math.floor);
		     * // => [1.2]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.differenceBy([{ 'x': 2 }, { 'x': 1 }], [{ 'x': 1 }], 'x');
		     * // => [{ 'x': 2 }]
		     */
		    var differenceBy = baseRest(function(array, values) {
		      var iteratee = last(values);
		      if (isArrayLikeObject(iteratee)) {
		        iteratee = undefined$1;
		      }
		      return isArrayLikeObject(array)
		        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), getIteratee(iteratee, 2))
		        : [];
		    });

		    /**
		     * This method is like `_.difference` except that it accepts `comparator`
		     * which is invoked to compare elements of `array` to `values`. The order and
		     * references of result values are determined by the first array. The comparator
		     * is invoked with two arguments: (arrVal, othVal).
		     *
		     * **Note:** Unlike `_.pullAllWith`, this method returns a new array.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {...Array} [values] The values to exclude.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of filtered values.
		     * @example
		     *
		     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
		     *
		     * _.differenceWith(objects, [{ 'x': 1, 'y': 2 }], _.isEqual);
		     * // => [{ 'x': 2, 'y': 1 }]
		     */
		    var differenceWith = baseRest(function(array, values) {
		      var comparator = last(values);
		      if (isArrayLikeObject(comparator)) {
		        comparator = undefined$1;
		      }
		      return isArrayLikeObject(array)
		        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), undefined$1, comparator)
		        : [];
		    });

		    /**
		     * Creates a slice of `array` with `n` elements dropped from the beginning.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.5.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {number} [n=1] The number of elements to drop.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * _.drop([1, 2, 3]);
		     * // => [2, 3]
		     *
		     * _.drop([1, 2, 3], 2);
		     * // => [3]
		     *
		     * _.drop([1, 2, 3], 5);
		     * // => []
		     *
		     * _.drop([1, 2, 3], 0);
		     * // => [1, 2, 3]
		     */
		    function drop(array, n, guard) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return [];
		      }
		      n = (guard || n === undefined$1) ? 1 : toInteger(n);
		      return baseSlice(array, n < 0 ? 0 : n, length);
		    }

		    /**
		     * Creates a slice of `array` with `n` elements dropped from the end.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {number} [n=1] The number of elements to drop.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * _.dropRight([1, 2, 3]);
		     * // => [1, 2]
		     *
		     * _.dropRight([1, 2, 3], 2);
		     * // => [1]
		     *
		     * _.dropRight([1, 2, 3], 5);
		     * // => []
		     *
		     * _.dropRight([1, 2, 3], 0);
		     * // => [1, 2, 3]
		     */
		    function dropRight(array, n, guard) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return [];
		      }
		      n = (guard || n === undefined$1) ? 1 : toInteger(n);
		      n = length - n;
		      return baseSlice(array, 0, n < 0 ? 0 : n);
		    }

		    /**
		     * Creates a slice of `array` excluding elements dropped from the end.
		     * Elements are dropped until `predicate` returns falsey. The predicate is
		     * invoked with three arguments: (value, index, array).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'active': true },
		     *   { 'user': 'fred',    'active': false },
		     *   { 'user': 'pebbles', 'active': false }
		     * ];
		     *
		     * _.dropRightWhile(users, function(o) { return !o.active; });
		     * // => objects for ['barney']
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.dropRightWhile(users, { 'user': 'pebbles', 'active': false });
		     * // => objects for ['barney', 'fred']
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.dropRightWhile(users, ['active', false]);
		     * // => objects for ['barney']
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.dropRightWhile(users, 'active');
		     * // => objects for ['barney', 'fred', 'pebbles']
		     */
		    function dropRightWhile(array, predicate) {
		      return (array && array.length)
		        ? baseWhile(array, getIteratee(predicate, 3), true, true)
		        : [];
		    }

		    /**
		     * Creates a slice of `array` excluding elements dropped from the beginning.
		     * Elements are dropped until `predicate` returns falsey. The predicate is
		     * invoked with three arguments: (value, index, array).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'active': false },
		     *   { 'user': 'fred',    'active': false },
		     *   { 'user': 'pebbles', 'active': true }
		     * ];
		     *
		     * _.dropWhile(users, function(o) { return !o.active; });
		     * // => objects for ['pebbles']
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.dropWhile(users, { 'user': 'barney', 'active': false });
		     * // => objects for ['fred', 'pebbles']
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.dropWhile(users, ['active', false]);
		     * // => objects for ['pebbles']
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.dropWhile(users, 'active');
		     * // => objects for ['barney', 'fred', 'pebbles']
		     */
		    function dropWhile(array, predicate) {
		      return (array && array.length)
		        ? baseWhile(array, getIteratee(predicate, 3), true)
		        : [];
		    }

		    /**
		     * Fills elements of `array` with `value` from `start` up to, but not
		     * including, `end`.
		     *
		     * **Note:** This method mutates `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.2.0
		     * @category Array
		     * @param {Array} array The array to fill.
		     * @param {*} value The value to fill `array` with.
		     * @param {number} [start=0] The start position.
		     * @param {number} [end=array.length] The end position.
		     * @returns {Array} Returns `array`.
		     * @example
		     *
		     * var array = [1, 2, 3];
		     *
		     * _.fill(array, 'a');
		     * console.log(array);
		     * // => ['a', 'a', 'a']
		     *
		     * _.fill(Array(3), 2);
		     * // => [2, 2, 2]
		     *
		     * _.fill([4, 6, 8, 10], '*', 1, 3);
		     * // => [4, '*', '*', 10]
		     */
		    function fill(array, value, start, end) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return [];
		      }
		      if (start && typeof start != 'number' && isIterateeCall(array, value, start)) {
		        start = 0;
		        end = length;
		      }
		      return baseFill(array, value, start, end);
		    }

		    /**
		     * This method is like `_.find` except that it returns the index of the first
		     * element `predicate` returns truthy for instead of the element itself.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.1.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @param {number} [fromIndex=0] The index to search from.
		     * @returns {number} Returns the index of the found element, else `-1`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'active': false },
		     *   { 'user': 'fred',    'active': false },
		     *   { 'user': 'pebbles', 'active': true }
		     * ];
		     *
		     * _.findIndex(users, function(o) { return o.user == 'barney'; });
		     * // => 0
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.findIndex(users, { 'user': 'fred', 'active': false });
		     * // => 1
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.findIndex(users, ['active', false]);
		     * // => 0
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.findIndex(users, 'active');
		     * // => 2
		     */
		    function findIndex(array, predicate, fromIndex) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return -1;
		      }
		      var index = fromIndex == null ? 0 : toInteger(fromIndex);
		      if (index < 0) {
		        index = nativeMax(length + index, 0);
		      }
		      return baseFindIndex(array, getIteratee(predicate, 3), index);
		    }

		    /**
		     * This method is like `_.findIndex` except that it iterates over elements
		     * of `collection` from right to left.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @param {number} [fromIndex=array.length-1] The index to search from.
		     * @returns {number} Returns the index of the found element, else `-1`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'active': true },
		     *   { 'user': 'fred',    'active': false },
		     *   { 'user': 'pebbles', 'active': false }
		     * ];
		     *
		     * _.findLastIndex(users, function(o) { return o.user == 'pebbles'; });
		     * // => 2
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.findLastIndex(users, { 'user': 'barney', 'active': true });
		     * // => 0
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.findLastIndex(users, ['active', false]);
		     * // => 2
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.findLastIndex(users, 'active');
		     * // => 0
		     */
		    function findLastIndex(array, predicate, fromIndex) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return -1;
		      }
		      var index = length - 1;
		      if (fromIndex !== undefined$1) {
		        index = toInteger(fromIndex);
		        index = fromIndex < 0
		          ? nativeMax(length + index, 0)
		          : nativeMin(index, length - 1);
		      }
		      return baseFindIndex(array, getIteratee(predicate, 3), index, true);
		    }

		    /**
		     * Flattens `array` a single level deep.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to flatten.
		     * @returns {Array} Returns the new flattened array.
		     * @example
		     *
		     * _.flatten([1, [2, [3, [4]], 5]]);
		     * // => [1, 2, [3, [4]], 5]
		     */
		    function flatten(array) {
		      var length = array == null ? 0 : array.length;
		      return length ? baseFlatten(array, 1) : [];
		    }

		    /**
		     * Recursively flattens `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to flatten.
		     * @returns {Array} Returns the new flattened array.
		     * @example
		     *
		     * _.flattenDeep([1, [2, [3, [4]], 5]]);
		     * // => [1, 2, 3, 4, 5]
		     */
		    function flattenDeep(array) {
		      var length = array == null ? 0 : array.length;
		      return length ? baseFlatten(array, INFINITY) : [];
		    }

		    /**
		     * Recursively flatten `array` up to `depth` times.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.4.0
		     * @category Array
		     * @param {Array} array The array to flatten.
		     * @param {number} [depth=1] The maximum recursion depth.
		     * @returns {Array} Returns the new flattened array.
		     * @example
		     *
		     * var array = [1, [2, [3, [4]], 5]];
		     *
		     * _.flattenDepth(array, 1);
		     * // => [1, 2, [3, [4]], 5]
		     *
		     * _.flattenDepth(array, 2);
		     * // => [1, 2, 3, [4], 5]
		     */
		    function flattenDepth(array, depth) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return [];
		      }
		      depth = depth === undefined$1 ? 1 : toInteger(depth);
		      return baseFlatten(array, depth);
		    }

		    /**
		     * The inverse of `_.toPairs`; this method returns an object composed
		     * from key-value `pairs`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} pairs The key-value pairs.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * _.fromPairs([['a', 1], ['b', 2]]);
		     * // => { 'a': 1, 'b': 2 }
		     */
		    function fromPairs(pairs) {
		      var index = -1,
		          length = pairs == null ? 0 : pairs.length,
		          result = {};

		      while (++index < length) {
		        var pair = pairs[index];
		        baseAssignValue(result, pair[0], pair[1]);
		      }
		      return result;
		    }

		    /**
		     * Gets the first element of `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @alias first
		     * @category Array
		     * @param {Array} array The array to query.
		     * @returns {*} Returns the first element of `array`.
		     * @example
		     *
		     * _.head([1, 2, 3]);
		     * // => 1
		     *
		     * _.head([]);
		     * // => undefined
		     */
		    function head(array) {
		      return (array && array.length) ? array[0] : undefined$1;
		    }

		    /**
		     * Gets the index at which the first occurrence of `value` is found in `array`
		     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons. If `fromIndex` is negative, it's used as the
		     * offset from the end of `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {*} value The value to search for.
		     * @param {number} [fromIndex=0] The index to search from.
		     * @returns {number} Returns the index of the matched value, else `-1`.
		     * @example
		     *
		     * _.indexOf([1, 2, 1, 2], 2);
		     * // => 1
		     *
		     * // Search from the `fromIndex`.
		     * _.indexOf([1, 2, 1, 2], 2, 2);
		     * // => 3
		     */
		    function indexOf(array, value, fromIndex) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return -1;
		      }
		      var index = fromIndex == null ? 0 : toInteger(fromIndex);
		      if (index < 0) {
		        index = nativeMax(length + index, 0);
		      }
		      return baseIndexOf(array, value, index);
		    }

		    /**
		     * Gets all but the last element of `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * _.initial([1, 2, 3]);
		     * // => [1, 2]
		     */
		    function initial(array) {
		      var length = array == null ? 0 : array.length;
		      return length ? baseSlice(array, 0, -1) : [];
		    }

		    /**
		     * Creates an array of unique values that are included in all given arrays
		     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons. The order and references of result values are
		     * determined by the first array.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @returns {Array} Returns the new array of intersecting values.
		     * @example
		     *
		     * _.intersection([2, 1], [2, 3]);
		     * // => [2]
		     */
		    var intersection = baseRest(function(arrays) {
		      var mapped = arrayMap(arrays, castArrayLikeObject);
		      return (mapped.length && mapped[0] === arrays[0])
		        ? baseIntersection(mapped)
		        : [];
		    });

		    /**
		     * This method is like `_.intersection` except that it accepts `iteratee`
		     * which is invoked for each element of each `arrays` to generate the criterion
		     * by which they're compared. The order and references of result values are
		     * determined by the first array. The iteratee is invoked with one argument:
		     * (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Array} Returns the new array of intersecting values.
		     * @example
		     *
		     * _.intersectionBy([2.1, 1.2], [2.3, 3.4], Math.floor);
		     * // => [2.1]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.intersectionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
		     * // => [{ 'x': 1 }]
		     */
		    var intersectionBy = baseRest(function(arrays) {
		      var iteratee = last(arrays),
		          mapped = arrayMap(arrays, castArrayLikeObject);

		      if (iteratee === last(mapped)) {
		        iteratee = undefined$1;
		      } else {
		        mapped.pop();
		      }
		      return (mapped.length && mapped[0] === arrays[0])
		        ? baseIntersection(mapped, getIteratee(iteratee, 2))
		        : [];
		    });

		    /**
		     * This method is like `_.intersection` except that it accepts `comparator`
		     * which is invoked to compare elements of `arrays`. The order and references
		     * of result values are determined by the first array. The comparator is
		     * invoked with two arguments: (arrVal, othVal).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of intersecting values.
		     * @example
		     *
		     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
		     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
		     *
		     * _.intersectionWith(objects, others, _.isEqual);
		     * // => [{ 'x': 1, 'y': 2 }]
		     */
		    var intersectionWith = baseRest(function(arrays) {
		      var comparator = last(arrays),
		          mapped = arrayMap(arrays, castArrayLikeObject);

		      comparator = typeof comparator == 'function' ? comparator : undefined$1;
		      if (comparator) {
		        mapped.pop();
		      }
		      return (mapped.length && mapped[0] === arrays[0])
		        ? baseIntersection(mapped, undefined$1, comparator)
		        : [];
		    });

		    /**
		     * Converts all elements in `array` into a string separated by `separator`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to convert.
		     * @param {string} [separator=','] The element separator.
		     * @returns {string} Returns the joined string.
		     * @example
		     *
		     * _.join(['a', 'b', 'c'], '~');
		     * // => 'a~b~c'
		     */
		    function join(array, separator) {
		      return array == null ? '' : nativeJoin.call(array, separator);
		    }

		    /**
		     * Gets the last element of `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @returns {*} Returns the last element of `array`.
		     * @example
		     *
		     * _.last([1, 2, 3]);
		     * // => 3
		     */
		    function last(array) {
		      var length = array == null ? 0 : array.length;
		      return length ? array[length - 1] : undefined$1;
		    }

		    /**
		     * This method is like `_.indexOf` except that it iterates over elements of
		     * `array` from right to left.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {*} value The value to search for.
		     * @param {number} [fromIndex=array.length-1] The index to search from.
		     * @returns {number} Returns the index of the matched value, else `-1`.
		     * @example
		     *
		     * _.lastIndexOf([1, 2, 1, 2], 2);
		     * // => 3
		     *
		     * // Search from the `fromIndex`.
		     * _.lastIndexOf([1, 2, 1, 2], 2, 2);
		     * // => 1
		     */
		    function lastIndexOf(array, value, fromIndex) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return -1;
		      }
		      var index = length;
		      if (fromIndex !== undefined$1) {
		        index = toInteger(fromIndex);
		        index = index < 0 ? nativeMax(length + index, 0) : nativeMin(index, length - 1);
		      }
		      return value === value
		        ? strictLastIndexOf(array, value, index)
		        : baseFindIndex(array, baseIsNaN, index, true);
		    }

		    /**
		     * Gets the element at index `n` of `array`. If `n` is negative, the nth
		     * element from the end is returned.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.11.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {number} [n=0] The index of the element to return.
		     * @returns {*} Returns the nth element of `array`.
		     * @example
		     *
		     * var array = ['a', 'b', 'c', 'd'];
		     *
		     * _.nth(array, 1);
		     * // => 'b'
		     *
		     * _.nth(array, -2);
		     * // => 'c';
		     */
		    function nth(array, n) {
		      return (array && array.length) ? baseNth(array, toInteger(n)) : undefined$1;
		    }

		    /**
		     * Removes all given values from `array` using
		     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons.
		     *
		     * **Note:** Unlike `_.without`, this method mutates `array`. Use `_.remove`
		     * to remove elements from an array by predicate.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @param {...*} [values] The values to remove.
		     * @returns {Array} Returns `array`.
		     * @example
		     *
		     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
		     *
		     * _.pull(array, 'a', 'c');
		     * console.log(array);
		     * // => ['b', 'b']
		     */
		    var pull = baseRest(pullAll);

		    /**
		     * This method is like `_.pull` except that it accepts an array of values to remove.
		     *
		     * **Note:** Unlike `_.difference`, this method mutates `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @param {Array} values The values to remove.
		     * @returns {Array} Returns `array`.
		     * @example
		     *
		     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
		     *
		     * _.pullAll(array, ['a', 'c']);
		     * console.log(array);
		     * // => ['b', 'b']
		     */
		    function pullAll(array, values) {
		      return (array && array.length && values && values.length)
		        ? basePullAll(array, values)
		        : array;
		    }

		    /**
		     * This method is like `_.pullAll` except that it accepts `iteratee` which is
		     * invoked for each element of `array` and `values` to generate the criterion
		     * by which they're compared. The iteratee is invoked with one argument: (value).
		     *
		     * **Note:** Unlike `_.differenceBy`, this method mutates `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @param {Array} values The values to remove.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Array} Returns `array`.
		     * @example
		     *
		     * var array = [{ 'x': 1 }, { 'x': 2 }, { 'x': 3 }, { 'x': 1 }];
		     *
		     * _.pullAllBy(array, [{ 'x': 1 }, { 'x': 3 }], 'x');
		     * console.log(array);
		     * // => [{ 'x': 2 }]
		     */
		    function pullAllBy(array, values, iteratee) {
		      return (array && array.length && values && values.length)
		        ? basePullAll(array, values, getIteratee(iteratee, 2))
		        : array;
		    }

		    /**
		     * This method is like `_.pullAll` except that it accepts `comparator` which
		     * is invoked to compare elements of `array` to `values`. The comparator is
		     * invoked with two arguments: (arrVal, othVal).
		     *
		     * **Note:** Unlike `_.differenceWith`, this method mutates `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.6.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @param {Array} values The values to remove.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns `array`.
		     * @example
		     *
		     * var array = [{ 'x': 1, 'y': 2 }, { 'x': 3, 'y': 4 }, { 'x': 5, 'y': 6 }];
		     *
		     * _.pullAllWith(array, [{ 'x': 3, 'y': 4 }], _.isEqual);
		     * console.log(array);
		     * // => [{ 'x': 1, 'y': 2 }, { 'x': 5, 'y': 6 }]
		     */
		    function pullAllWith(array, values, comparator) {
		      return (array && array.length && values && values.length)
		        ? basePullAll(array, values, undefined$1, comparator)
		        : array;
		    }

		    /**
		     * Removes elements from `array` corresponding to `indexes` and returns an
		     * array of removed elements.
		     *
		     * **Note:** Unlike `_.at`, this method mutates `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @param {...(number|number[])} [indexes] The indexes of elements to remove.
		     * @returns {Array} Returns the new array of removed elements.
		     * @example
		     *
		     * var array = ['a', 'b', 'c', 'd'];
		     * var pulled = _.pullAt(array, [1, 3]);
		     *
		     * console.log(array);
		     * // => ['a', 'c']
		     *
		     * console.log(pulled);
		     * // => ['b', 'd']
		     */
		    var pullAt = flatRest(function(array, indexes) {
		      var length = array == null ? 0 : array.length,
		          result = baseAt(array, indexes);

		      basePullAt(array, arrayMap(indexes, function(index) {
		        return isIndex(index, length) ? +index : index;
		      }).sort(compareAscending));

		      return result;
		    });

		    /**
		     * Removes all elements from `array` that `predicate` returns truthy for
		     * and returns an array of the removed elements. The predicate is invoked
		     * with three arguments: (value, index, array).
		     *
		     * **Note:** Unlike `_.filter`, this method mutates `array`. Use `_.pull`
		     * to pull elements from an array by value.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the new array of removed elements.
		     * @example
		     *
		     * var array = [1, 2, 3, 4];
		     * var evens = _.remove(array, function(n) {
		     *   return n % 2 == 0;
		     * });
		     *
		     * console.log(array);
		     * // => [1, 3]
		     *
		     * console.log(evens);
		     * // => [2, 4]
		     */
		    function remove(array, predicate) {
		      var result = [];
		      if (!(array && array.length)) {
		        return result;
		      }
		      var index = -1,
		          indexes = [],
		          length = array.length;

		      predicate = getIteratee(predicate, 3);
		      while (++index < length) {
		        var value = array[index];
		        if (predicate(value, index, array)) {
		          result.push(value);
		          indexes.push(index);
		        }
		      }
		      basePullAt(array, indexes);
		      return result;
		    }

		    /**
		     * Reverses `array` so that the first element becomes the last, the second
		     * element becomes the second to last, and so on.
		     *
		     * **Note:** This method mutates `array` and is based on
		     * [`Array#reverse`](https://mdn.io/Array/reverse).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to modify.
		     * @returns {Array} Returns `array`.
		     * @example
		     *
		     * var array = [1, 2, 3];
		     *
		     * _.reverse(array);
		     * // => [3, 2, 1]
		     *
		     * console.log(array);
		     * // => [3, 2, 1]
		     */
		    function reverse(array) {
		      return array == null ? array : nativeReverse.call(array);
		    }

		    /**
		     * Creates a slice of `array` from `start` up to, but not including, `end`.
		     *
		     * **Note:** This method is used instead of
		     * [`Array#slice`](https://mdn.io/Array/slice) to ensure dense arrays are
		     * returned.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to slice.
		     * @param {number} [start=0] The start position.
		     * @param {number} [end=array.length] The end position.
		     * @returns {Array} Returns the slice of `array`.
		     */
		    function slice(array, start, end) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return [];
		      }
		      if (end && typeof end != 'number' && isIterateeCall(array, start, end)) {
		        start = 0;
		        end = length;
		      }
		      else {
		        start = start == null ? 0 : toInteger(start);
		        end = end === undefined$1 ? length : toInteger(end);
		      }
		      return baseSlice(array, start, end);
		    }

		    /**
		     * Uses a binary search to determine the lowest index at which `value`
		     * should be inserted into `array` in order to maintain its sort order.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The sorted array to inspect.
		     * @param {*} value The value to evaluate.
		     * @returns {number} Returns the index at which `value` should be inserted
		     *  into `array`.
		     * @example
		     *
		     * _.sortedIndex([30, 50], 40);
		     * // => 1
		     */
		    function sortedIndex(array, value) {
		      return baseSortedIndex(array, value);
		    }

		    /**
		     * This method is like `_.sortedIndex` except that it accepts `iteratee`
		     * which is invoked for `value` and each element of `array` to compute their
		     * sort ranking. The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The sorted array to inspect.
		     * @param {*} value The value to evaluate.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {number} Returns the index at which `value` should be inserted
		     *  into `array`.
		     * @example
		     *
		     * var objects = [{ 'x': 4 }, { 'x': 5 }];
		     *
		     * _.sortedIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
		     * // => 0
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.sortedIndexBy(objects, { 'x': 4 }, 'x');
		     * // => 0
		     */
		    function sortedIndexBy(array, value, iteratee) {
		      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2));
		    }

		    /**
		     * This method is like `_.indexOf` except that it performs a binary
		     * search on a sorted `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {*} value The value to search for.
		     * @returns {number} Returns the index of the matched value, else `-1`.
		     * @example
		     *
		     * _.sortedIndexOf([4, 5, 5, 5, 6], 5);
		     * // => 1
		     */
		    function sortedIndexOf(array, value) {
		      var length = array == null ? 0 : array.length;
		      if (length) {
		        var index = baseSortedIndex(array, value);
		        if (index < length && eq(array[index], value)) {
		          return index;
		        }
		      }
		      return -1;
		    }

		    /**
		     * This method is like `_.sortedIndex` except that it returns the highest
		     * index at which `value` should be inserted into `array` in order to
		     * maintain its sort order.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The sorted array to inspect.
		     * @param {*} value The value to evaluate.
		     * @returns {number} Returns the index at which `value` should be inserted
		     *  into `array`.
		     * @example
		     *
		     * _.sortedLastIndex([4, 5, 5, 5, 6], 5);
		     * // => 4
		     */
		    function sortedLastIndex(array, value) {
		      return baseSortedIndex(array, value, true);
		    }

		    /**
		     * This method is like `_.sortedLastIndex` except that it accepts `iteratee`
		     * which is invoked for `value` and each element of `array` to compute their
		     * sort ranking. The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The sorted array to inspect.
		     * @param {*} value The value to evaluate.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {number} Returns the index at which `value` should be inserted
		     *  into `array`.
		     * @example
		     *
		     * var objects = [{ 'x': 4 }, { 'x': 5 }];
		     *
		     * _.sortedLastIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
		     * // => 1
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.sortedLastIndexBy(objects, { 'x': 4 }, 'x');
		     * // => 1
		     */
		    function sortedLastIndexBy(array, value, iteratee) {
		      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2), true);
		    }

		    /**
		     * This method is like `_.lastIndexOf` except that it performs a binary
		     * search on a sorted `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {*} value The value to search for.
		     * @returns {number} Returns the index of the matched value, else `-1`.
		     * @example
		     *
		     * _.sortedLastIndexOf([4, 5, 5, 5, 6], 5);
		     * // => 3
		     */
		    function sortedLastIndexOf(array, value) {
		      var length = array == null ? 0 : array.length;
		      if (length) {
		        var index = baseSortedIndex(array, value, true) - 1;
		        if (eq(array[index], value)) {
		          return index;
		        }
		      }
		      return -1;
		    }

		    /**
		     * This method is like `_.uniq` except that it's designed and optimized
		     * for sorted arrays.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @returns {Array} Returns the new duplicate free array.
		     * @example
		     *
		     * _.sortedUniq([1, 1, 2]);
		     * // => [1, 2]
		     */
		    function sortedUniq(array) {
		      return (array && array.length)
		        ? baseSortedUniq(array)
		        : [];
		    }

		    /**
		     * This method is like `_.uniqBy` except that it's designed and optimized
		     * for sorted arrays.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {Function} [iteratee] The iteratee invoked per element.
		     * @returns {Array} Returns the new duplicate free array.
		     * @example
		     *
		     * _.sortedUniqBy([1.1, 1.2, 2.3, 2.4], Math.floor);
		     * // => [1.1, 2.3]
		     */
		    function sortedUniqBy(array, iteratee) {
		      return (array && array.length)
		        ? baseSortedUniq(array, getIteratee(iteratee, 2))
		        : [];
		    }

		    /**
		     * Gets all but the first element of `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * _.tail([1, 2, 3]);
		     * // => [2, 3]
		     */
		    function tail(array) {
		      var length = array == null ? 0 : array.length;
		      return length ? baseSlice(array, 1, length) : [];
		    }

		    /**
		     * Creates a slice of `array` with `n` elements taken from the beginning.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {number} [n=1] The number of elements to take.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * _.take([1, 2, 3]);
		     * // => [1]
		     *
		     * _.take([1, 2, 3], 2);
		     * // => [1, 2]
		     *
		     * _.take([1, 2, 3], 5);
		     * // => [1, 2, 3]
		     *
		     * _.take([1, 2, 3], 0);
		     * // => []
		     */
		    function take(array, n, guard) {
		      if (!(array && array.length)) {
		        return [];
		      }
		      n = (guard || n === undefined$1) ? 1 : toInteger(n);
		      return baseSlice(array, 0, n < 0 ? 0 : n);
		    }

		    /**
		     * Creates a slice of `array` with `n` elements taken from the end.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {number} [n=1] The number of elements to take.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * _.takeRight([1, 2, 3]);
		     * // => [3]
		     *
		     * _.takeRight([1, 2, 3], 2);
		     * // => [2, 3]
		     *
		     * _.takeRight([1, 2, 3], 5);
		     * // => [1, 2, 3]
		     *
		     * _.takeRight([1, 2, 3], 0);
		     * // => []
		     */
		    function takeRight(array, n, guard) {
		      var length = array == null ? 0 : array.length;
		      if (!length) {
		        return [];
		      }
		      n = (guard || n === undefined$1) ? 1 : toInteger(n);
		      n = length - n;
		      return baseSlice(array, n < 0 ? 0 : n, length);
		    }

		    /**
		     * Creates a slice of `array` with elements taken from the end. Elements are
		     * taken until `predicate` returns falsey. The predicate is invoked with
		     * three arguments: (value, index, array).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'active': true },
		     *   { 'user': 'fred',    'active': false },
		     *   { 'user': 'pebbles', 'active': false }
		     * ];
		     *
		     * _.takeRightWhile(users, function(o) { return !o.active; });
		     * // => objects for ['fred', 'pebbles']
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.takeRightWhile(users, { 'user': 'pebbles', 'active': false });
		     * // => objects for ['pebbles']
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.takeRightWhile(users, ['active', false]);
		     * // => objects for ['fred', 'pebbles']
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.takeRightWhile(users, 'active');
		     * // => []
		     */
		    function takeRightWhile(array, predicate) {
		      return (array && array.length)
		        ? baseWhile(array, getIteratee(predicate, 3), false, true)
		        : [];
		    }

		    /**
		     * Creates a slice of `array` with elements taken from the beginning. Elements
		     * are taken until `predicate` returns falsey. The predicate is invoked with
		     * three arguments: (value, index, array).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Array
		     * @param {Array} array The array to query.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the slice of `array`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'active': false },
		     *   { 'user': 'fred',    'active': false },
		     *   { 'user': 'pebbles', 'active': true }
		     * ];
		     *
		     * _.takeWhile(users, function(o) { return !o.active; });
		     * // => objects for ['barney', 'fred']
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.takeWhile(users, { 'user': 'barney', 'active': false });
		     * // => objects for ['barney']
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.takeWhile(users, ['active', false]);
		     * // => objects for ['barney', 'fred']
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.takeWhile(users, 'active');
		     * // => []
		     */
		    function takeWhile(array, predicate) {
		      return (array && array.length)
		        ? baseWhile(array, getIteratee(predicate, 3))
		        : [];
		    }

		    /**
		     * Creates an array of unique values, in order, from all given arrays using
		     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @returns {Array} Returns the new array of combined values.
		     * @example
		     *
		     * _.union([2], [1, 2]);
		     * // => [2, 1]
		     */
		    var union = baseRest(function(arrays) {
		      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true));
		    });

		    /**
		     * This method is like `_.union` except that it accepts `iteratee` which is
		     * invoked for each element of each `arrays` to generate the criterion by
		     * which uniqueness is computed. Result values are chosen from the first
		     * array in which the value occurs. The iteratee is invoked with one argument:
		     * (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Array} Returns the new array of combined values.
		     * @example
		     *
		     * _.unionBy([2.1], [1.2, 2.3], Math.floor);
		     * // => [2.1, 1.2]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.unionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
		     * // => [{ 'x': 1 }, { 'x': 2 }]
		     */
		    var unionBy = baseRest(function(arrays) {
		      var iteratee = last(arrays);
		      if (isArrayLikeObject(iteratee)) {
		        iteratee = undefined$1;
		      }
		      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), getIteratee(iteratee, 2));
		    });

		    /**
		     * This method is like `_.union` except that it accepts `comparator` which
		     * is invoked to compare elements of `arrays`. Result values are chosen from
		     * the first array in which the value occurs. The comparator is invoked
		     * with two arguments: (arrVal, othVal).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of combined values.
		     * @example
		     *
		     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
		     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
		     *
		     * _.unionWith(objects, others, _.isEqual);
		     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
		     */
		    var unionWith = baseRest(function(arrays) {
		      var comparator = last(arrays);
		      comparator = typeof comparator == 'function' ? comparator : undefined$1;
		      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), undefined$1, comparator);
		    });

		    /**
		     * Creates a duplicate-free version of an array, using
		     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons, in which only the first occurrence of each element
		     * is kept. The order of result values is determined by the order they occur
		     * in the array.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @returns {Array} Returns the new duplicate free array.
		     * @example
		     *
		     * _.uniq([2, 1, 2]);
		     * // => [2, 1]
		     */
		    function uniq(array) {
		      return (array && array.length) ? baseUniq(array) : [];
		    }

		    /**
		     * This method is like `_.uniq` except that it accepts `iteratee` which is
		     * invoked for each element in `array` to generate the criterion by which
		     * uniqueness is computed. The order of result values is determined by the
		     * order they occur in the array. The iteratee is invoked with one argument:
		     * (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Array} Returns the new duplicate free array.
		     * @example
		     *
		     * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
		     * // => [2.1, 1.2]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
		     * // => [{ 'x': 1 }, { 'x': 2 }]
		     */
		    function uniqBy(array, iteratee) {
		      return (array && array.length) ? baseUniq(array, getIteratee(iteratee, 2)) : [];
		    }

		    /**
		     * This method is like `_.uniq` except that it accepts `comparator` which
		     * is invoked to compare elements of `array`. The order of result values is
		     * determined by the order they occur in the array.The comparator is invoked
		     * with two arguments: (arrVal, othVal).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new duplicate free array.
		     * @example
		     *
		     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 2 }];
		     *
		     * _.uniqWith(objects, _.isEqual);
		     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
		     */
		    function uniqWith(array, comparator) {
		      comparator = typeof comparator == 'function' ? comparator : undefined$1;
		      return (array && array.length) ? baseUniq(array, undefined$1, comparator) : [];
		    }

		    /**
		     * This method is like `_.zip` except that it accepts an array of grouped
		     * elements and creates an array regrouping the elements to their pre-zip
		     * configuration.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.2.0
		     * @category Array
		     * @param {Array} array The array of grouped elements to process.
		     * @returns {Array} Returns the new array of regrouped elements.
		     * @example
		     *
		     * var zipped = _.zip(['a', 'b'], [1, 2], [true, false]);
		     * // => [['a', 1, true], ['b', 2, false]]
		     *
		     * _.unzip(zipped);
		     * // => [['a', 'b'], [1, 2], [true, false]]
		     */
		    function unzip(array) {
		      if (!(array && array.length)) {
		        return [];
		      }
		      var length = 0;
		      array = arrayFilter(array, function(group) {
		        if (isArrayLikeObject(group)) {
		          length = nativeMax(group.length, length);
		          return true;
		        }
		      });
		      return baseTimes(length, function(index) {
		        return arrayMap(array, baseProperty(index));
		      });
		    }

		    /**
		     * This method is like `_.unzip` except that it accepts `iteratee` to specify
		     * how regrouped values should be combined. The iteratee is invoked with the
		     * elements of each group: (...group).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.8.0
		     * @category Array
		     * @param {Array} array The array of grouped elements to process.
		     * @param {Function} [iteratee=_.identity] The function to combine
		     *  regrouped values.
		     * @returns {Array} Returns the new array of regrouped elements.
		     * @example
		     *
		     * var zipped = _.zip([1, 2], [10, 20], [100, 200]);
		     * // => [[1, 10, 100], [2, 20, 200]]
		     *
		     * _.unzipWith(zipped, _.add);
		     * // => [3, 30, 300]
		     */
		    function unzipWith(array, iteratee) {
		      if (!(array && array.length)) {
		        return [];
		      }
		      var result = unzip(array);
		      if (iteratee == null) {
		        return result;
		      }
		      return arrayMap(result, function(group) {
		        return apply(iteratee, undefined$1, group);
		      });
		    }

		    /**
		     * Creates an array excluding all given values using
		     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * for equality comparisons.
		     *
		     * **Note:** Unlike `_.pull`, this method returns a new array.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {Array} array The array to inspect.
		     * @param {...*} [values] The values to exclude.
		     * @returns {Array} Returns the new array of filtered values.
		     * @see _.difference, _.xor
		     * @example
		     *
		     * _.without([2, 1, 2, 3], 1, 2);
		     * // => [3]
		     */
		    var without = baseRest(function(array, values) {
		      return isArrayLikeObject(array)
		        ? baseDifference(array, values)
		        : [];
		    });

		    /**
		     * Creates an array of unique values that is the
		     * [symmetric difference](https://en.wikipedia.org/wiki/Symmetric_difference)
		     * of the given arrays. The order of result values is determined by the order
		     * they occur in the arrays.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.4.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @returns {Array} Returns the new array of filtered values.
		     * @see _.difference, _.without
		     * @example
		     *
		     * _.xor([2, 1], [2, 3]);
		     * // => [1, 3]
		     */
		    var xor = baseRest(function(arrays) {
		      return baseXor(arrayFilter(arrays, isArrayLikeObject));
		    });

		    /**
		     * This method is like `_.xor` except that it accepts `iteratee` which is
		     * invoked for each element of each `arrays` to generate the criterion by
		     * which by which they're compared. The order of result values is determined
		     * by the order they occur in the arrays. The iteratee is invoked with one
		     * argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Array} Returns the new array of filtered values.
		     * @example
		     *
		     * _.xorBy([2.1, 1.2], [2.3, 3.4], Math.floor);
		     * // => [1.2, 3.4]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.xorBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
		     * // => [{ 'x': 2 }]
		     */
		    var xorBy = baseRest(function(arrays) {
		      var iteratee = last(arrays);
		      if (isArrayLikeObject(iteratee)) {
		        iteratee = undefined$1;
		      }
		      return baseXor(arrayFilter(arrays, isArrayLikeObject), getIteratee(iteratee, 2));
		    });

		    /**
		     * This method is like `_.xor` except that it accepts `comparator` which is
		     * invoked to compare elements of `arrays`. The order of result values is
		     * determined by the order they occur in the arrays. The comparator is invoked
		     * with two arguments: (arrVal, othVal).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to inspect.
		     * @param {Function} [comparator] The comparator invoked per element.
		     * @returns {Array} Returns the new array of filtered values.
		     * @example
		     *
		     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
		     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
		     *
		     * _.xorWith(objects, others, _.isEqual);
		     * // => [{ 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
		     */
		    var xorWith = baseRest(function(arrays) {
		      var comparator = last(arrays);
		      comparator = typeof comparator == 'function' ? comparator : undefined$1;
		      return baseXor(arrayFilter(arrays, isArrayLikeObject), undefined$1, comparator);
		    });

		    /**
		     * Creates an array of grouped elements, the first of which contains the
		     * first elements of the given arrays, the second of which contains the
		     * second elements of the given arrays, and so on.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to process.
		     * @returns {Array} Returns the new array of grouped elements.
		     * @example
		     *
		     * _.zip(['a', 'b'], [1, 2], [true, false]);
		     * // => [['a', 1, true], ['b', 2, false]]
		     */
		    var zip = baseRest(unzip);

		    /**
		     * This method is like `_.fromPairs` except that it accepts two arrays,
		     * one of property identifiers and one of corresponding values.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.4.0
		     * @category Array
		     * @param {Array} [props=[]] The property identifiers.
		     * @param {Array} [values=[]] The property values.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * _.zipObject(['a', 'b'], [1, 2]);
		     * // => { 'a': 1, 'b': 2 }
		     */
		    function zipObject(props, values) {
		      return baseZipObject(props || [], values || [], assignValue);
		    }

		    /**
		     * This method is like `_.zipObject` except that it supports property paths.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.1.0
		     * @category Array
		     * @param {Array} [props=[]] The property identifiers.
		     * @param {Array} [values=[]] The property values.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * _.zipObjectDeep(['a.b[0].c', 'a.b[1].d'], [1, 2]);
		     * // => { 'a': { 'b': [{ 'c': 1 }, { 'd': 2 }] } }
		     */
		    function zipObjectDeep(props, values) {
		      return baseZipObject(props || [], values || [], baseSet);
		    }

		    /**
		     * This method is like `_.zip` except that it accepts `iteratee` to specify
		     * how grouped values should be combined. The iteratee is invoked with the
		     * elements of each group: (...group).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.8.0
		     * @category Array
		     * @param {...Array} [arrays] The arrays to process.
		     * @param {Function} [iteratee=_.identity] The function to combine
		     *  grouped values.
		     * @returns {Array} Returns the new array of grouped elements.
		     * @example
		     *
		     * _.zipWith([1, 2], [10, 20], [100, 200], function(a, b, c) {
		     *   return a + b + c;
		     * });
		     * // => [111, 222]
		     */
		    var zipWith = baseRest(function(arrays) {
		      var length = arrays.length,
		          iteratee = length > 1 ? arrays[length - 1] : undefined$1;

		      iteratee = typeof iteratee == 'function' ? (arrays.pop(), iteratee) : undefined$1;
		      return unzipWith(arrays, iteratee);
		    });

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates a `lodash` wrapper instance that wraps `value` with explicit method
		     * chain sequences enabled. The result of such sequences must be unwrapped
		     * with `_#value`.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.3.0
		     * @category Seq
		     * @param {*} value The value to wrap.
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'age': 36 },
		     *   { 'user': 'fred',    'age': 40 },
		     *   { 'user': 'pebbles', 'age': 1 }
		     * ];
		     *
		     * var youngest = _
		     *   .chain(users)
		     *   .sortBy('age')
		     *   .map(function(o) {
		     *     return o.user + ' is ' + o.age;
		     *   })
		     *   .head()
		     *   .value();
		     * // => 'pebbles is 1'
		     */
		    function chain(value) {
		      var result = lodash(value);
		      result.__chain__ = true;
		      return result;
		    }

		    /**
		     * This method invokes `interceptor` and returns `value`. The interceptor
		     * is invoked with one argument; (value). The purpose of this method is to
		     * "tap into" a method chain sequence in order to modify intermediate results.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Seq
		     * @param {*} value The value to provide to `interceptor`.
		     * @param {Function} interceptor The function to invoke.
		     * @returns {*} Returns `value`.
		     * @example
		     *
		     * _([1, 2, 3])
		     *  .tap(function(array) {
		     *    // Mutate input array.
		     *    array.pop();
		     *  })
		     *  .reverse()
		     *  .value();
		     * // => [2, 1]
		     */
		    function tap(value, interceptor) {
		      interceptor(value);
		      return value;
		    }

		    /**
		     * This method is like `_.tap` except that it returns the result of `interceptor`.
		     * The purpose of this method is to "pass thru" values replacing intermediate
		     * results in a method chain sequence.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Seq
		     * @param {*} value The value to provide to `interceptor`.
		     * @param {Function} interceptor The function to invoke.
		     * @returns {*} Returns the result of `interceptor`.
		     * @example
		     *
		     * _('  abc  ')
		     *  .chain()
		     *  .trim()
		     *  .thru(function(value) {
		     *    return [value];
		     *  })
		     *  .value();
		     * // => ['abc']
		     */
		    function thru(value, interceptor) {
		      return interceptor(value);
		    }

		    /**
		     * This method is the wrapper version of `_.at`.
		     *
		     * @name at
		     * @memberOf _
		     * @since 1.0.0
		     * @category Seq
		     * @param {...(string|string[])} [paths] The property paths to pick.
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
		     *
		     * _(object).at(['a[0].b.c', 'a[1]']).value();
		     * // => [3, 4]
		     */
		    var wrapperAt = flatRest(function(paths) {
		      var length = paths.length,
		          start = length ? paths[0] : 0,
		          value = this.__wrapped__,
		          interceptor = function(object) { return baseAt(object, paths); };

		      if (length > 1 || this.__actions__.length ||
		          !(value instanceof LazyWrapper) || !isIndex(start)) {
		        return this.thru(interceptor);
		      }
		      value = value.slice(start, +start + (length ? 1 : 0));
		      value.__actions__.push({
		        'func': thru,
		        'args': [interceptor],
		        'thisArg': undefined$1
		      });
		      return new LodashWrapper(value, this.__chain__).thru(function(array) {
		        if (length && !array.length) {
		          array.push(undefined$1);
		        }
		        return array;
		      });
		    });

		    /**
		     * Creates a `lodash` wrapper instance with explicit method chain sequences enabled.
		     *
		     * @name chain
		     * @memberOf _
		     * @since 0.1.0
		     * @category Seq
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney', 'age': 36 },
		     *   { 'user': 'fred',   'age': 40 }
		     * ];
		     *
		     * // A sequence without explicit chaining.
		     * _(users).head();
		     * // => { 'user': 'barney', 'age': 36 }
		     *
		     * // A sequence with explicit chaining.
		     * _(users)
		     *   .chain()
		     *   .head()
		     *   .pick('user')
		     *   .value();
		     * // => { 'user': 'barney' }
		     */
		    function wrapperChain() {
		      return chain(this);
		    }

		    /**
		     * Executes the chain sequence and returns the wrapped result.
		     *
		     * @name commit
		     * @memberOf _
		     * @since 3.2.0
		     * @category Seq
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * var array = [1, 2];
		     * var wrapped = _(array).push(3);
		     *
		     * console.log(array);
		     * // => [1, 2]
		     *
		     * wrapped = wrapped.commit();
		     * console.log(array);
		     * // => [1, 2, 3]
		     *
		     * wrapped.last();
		     * // => 3
		     *
		     * console.log(array);
		     * // => [1, 2, 3]
		     */
		    function wrapperCommit() {
		      return new LodashWrapper(this.value(), this.__chain__);
		    }

		    /**
		     * Gets the next value on a wrapped object following the
		     * [iterator protocol](https://mdn.io/iteration_protocols#iterator).
		     *
		     * @name next
		     * @memberOf _
		     * @since 4.0.0
		     * @category Seq
		     * @returns {Object} Returns the next iterator value.
		     * @example
		     *
		     * var wrapped = _([1, 2]);
		     *
		     * wrapped.next();
		     * // => { 'done': false, 'value': 1 }
		     *
		     * wrapped.next();
		     * // => { 'done': false, 'value': 2 }
		     *
		     * wrapped.next();
		     * // => { 'done': true, 'value': undefined }
		     */
		    function wrapperNext() {
		      if (this.__values__ === undefined$1) {
		        this.__values__ = toArray(this.value());
		      }
		      var done = this.__index__ >= this.__values__.length,
		          value = done ? undefined$1 : this.__values__[this.__index__++];

		      return { 'done': done, 'value': value };
		    }

		    /**
		     * Enables the wrapper to be iterable.
		     *
		     * @name Symbol.iterator
		     * @memberOf _
		     * @since 4.0.0
		     * @category Seq
		     * @returns {Object} Returns the wrapper object.
		     * @example
		     *
		     * var wrapped = _([1, 2]);
		     *
		     * wrapped[Symbol.iterator]() === wrapped;
		     * // => true
		     *
		     * Array.from(wrapped);
		     * // => [1, 2]
		     */
		    function wrapperToIterator() {
		      return this;
		    }

		    /**
		     * Creates a clone of the chain sequence planting `value` as the wrapped value.
		     *
		     * @name plant
		     * @memberOf _
		     * @since 3.2.0
		     * @category Seq
		     * @param {*} value The value to plant.
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * function square(n) {
		     *   return n * n;
		     * }
		     *
		     * var wrapped = _([1, 2]).map(square);
		     * var other = wrapped.plant([3, 4]);
		     *
		     * other.value();
		     * // => [9, 16]
		     *
		     * wrapped.value();
		     * // => [1, 4]
		     */
		    function wrapperPlant(value) {
		      var result,
		          parent = this;

		      while (parent instanceof baseLodash) {
		        var clone = wrapperClone(parent);
		        clone.__index__ = 0;
		        clone.__values__ = undefined$1;
		        if (result) {
		          previous.__wrapped__ = clone;
		        } else {
		          result = clone;
		        }
		        var previous = clone;
		        parent = parent.__wrapped__;
		      }
		      previous.__wrapped__ = value;
		      return result;
		    }

		    /**
		     * This method is the wrapper version of `_.reverse`.
		     *
		     * **Note:** This method mutates the wrapped array.
		     *
		     * @name reverse
		     * @memberOf _
		     * @since 0.1.0
		     * @category Seq
		     * @returns {Object} Returns the new `lodash` wrapper instance.
		     * @example
		     *
		     * var array = [1, 2, 3];
		     *
		     * _(array).reverse().value()
		     * // => [3, 2, 1]
		     *
		     * console.log(array);
		     * // => [3, 2, 1]
		     */
		    function wrapperReverse() {
		      var value = this.__wrapped__;
		      if (value instanceof LazyWrapper) {
		        var wrapped = value;
		        if (this.__actions__.length) {
		          wrapped = new LazyWrapper(this);
		        }
		        wrapped = wrapped.reverse();
		        wrapped.__actions__.push({
		          'func': thru,
		          'args': [reverse],
		          'thisArg': undefined$1
		        });
		        return new LodashWrapper(wrapped, this.__chain__);
		      }
		      return this.thru(reverse);
		    }

		    /**
		     * Executes the chain sequence to resolve the unwrapped value.
		     *
		     * @name value
		     * @memberOf _
		     * @since 0.1.0
		     * @alias toJSON, valueOf
		     * @category Seq
		     * @returns {*} Returns the resolved unwrapped value.
		     * @example
		     *
		     * _([1, 2, 3]).value();
		     * // => [1, 2, 3]
		     */
		    function wrapperValue() {
		      return baseWrapperValue(this.__wrapped__, this.__actions__);
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Creates an object composed of keys generated from the results of running
		     * each element of `collection` thru `iteratee`. The corresponding value of
		     * each key is the number of times the key was returned by `iteratee`. The
		     * iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.5.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
		     * @returns {Object} Returns the composed aggregate object.
		     * @example
		     *
		     * _.countBy([6.1, 4.2, 6.3], Math.floor);
		     * // => { '4': 1, '6': 2 }
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.countBy(['one', 'two', 'three'], 'length');
		     * // => { '3': 2, '5': 1 }
		     */
		    var countBy = createAggregator(function(result, value, key) {
		      if (hasOwnProperty.call(result, key)) {
		        ++result[key];
		      } else {
		        baseAssignValue(result, key, 1);
		      }
		    });

		    /**
		     * Checks if `predicate` returns truthy for **all** elements of `collection`.
		     * Iteration is stopped once `predicate` returns falsey. The predicate is
		     * invoked with three arguments: (value, index|key, collection).
		     *
		     * **Note:** This method returns `true` for
		     * [empty collections](https://en.wikipedia.org/wiki/Empty_set) because
		     * [everything is true](https://en.wikipedia.org/wiki/Vacuous_truth) of
		     * elements of empty collections.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {boolean} Returns `true` if all elements pass the predicate check,
		     *  else `false`.
		     * @example
		     *
		     * _.every([true, 1, null, 'yes'], Boolean);
		     * // => false
		     *
		     * var users = [
		     *   { 'user': 'barney', 'age': 36, 'active': false },
		     *   { 'user': 'fred',   'age': 40, 'active': false }
		     * ];
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.every(users, { 'user': 'barney', 'active': false });
		     * // => false
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.every(users, ['active', false]);
		     * // => true
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.every(users, 'active');
		     * // => false
		     */
		    function every(collection, predicate, guard) {
		      var func = isArray(collection) ? arrayEvery : baseEvery;
		      if (guard && isIterateeCall(collection, predicate, guard)) {
		        predicate = undefined$1;
		      }
		      return func(collection, getIteratee(predicate, 3));
		    }

		    /**
		     * Iterates over elements of `collection`, returning an array of all elements
		     * `predicate` returns truthy for. The predicate is invoked with three
		     * arguments: (value, index|key, collection).
		     *
		     * **Note:** Unlike `_.remove`, this method returns a new array.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the new filtered array.
		     * @see _.reject
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney', 'age': 36, 'active': true },
		     *   { 'user': 'fred',   'age': 40, 'active': false }
		     * ];
		     *
		     * _.filter(users, function(o) { return !o.active; });
		     * // => objects for ['fred']
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.filter(users, { 'age': 36, 'active': true });
		     * // => objects for ['barney']
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.filter(users, ['active', false]);
		     * // => objects for ['fred']
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.filter(users, 'active');
		     * // => objects for ['barney']
		     *
		     * // Combining several predicates using `_.overEvery` or `_.overSome`.
		     * _.filter(users, _.overSome([{ 'age': 36 }, ['age', 40]]));
		     * // => objects for ['fred', 'barney']
		     */
		    function filter(collection, predicate) {
		      var func = isArray(collection) ? arrayFilter : baseFilter;
		      return func(collection, getIteratee(predicate, 3));
		    }

		    /**
		     * Iterates over elements of `collection`, returning the first element
		     * `predicate` returns truthy for. The predicate is invoked with three
		     * arguments: (value, index|key, collection).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to inspect.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @param {number} [fromIndex=0] The index to search from.
		     * @returns {*} Returns the matched element, else `undefined`.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'age': 36, 'active': true },
		     *   { 'user': 'fred',    'age': 40, 'active': false },
		     *   { 'user': 'pebbles', 'age': 1,  'active': true }
		     * ];
		     *
		     * _.find(users, function(o) { return o.age < 40; });
		     * // => object for 'barney'
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.find(users, { 'age': 1, 'active': true });
		     * // => object for 'pebbles'
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.find(users, ['active', false]);
		     * // => object for 'fred'
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.find(users, 'active');
		     * // => object for 'barney'
		     */
		    var find = createFind(findIndex);

		    /**
		     * This method is like `_.find` except that it iterates over elements of
		     * `collection` from right to left.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to inspect.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @param {number} [fromIndex=collection.length-1] The index to search from.
		     * @returns {*} Returns the matched element, else `undefined`.
		     * @example
		     *
		     * _.findLast([1, 2, 3, 4], function(n) {
		     *   return n % 2 == 1;
		     * });
		     * // => 3
		     */
		    var findLast = createFind(findLastIndex);

		    /**
		     * Creates a flattened array of values by running each element in `collection`
		     * thru `iteratee` and flattening the mapped results. The iteratee is invoked
		     * with three arguments: (value, index|key, collection).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the new flattened array.
		     * @example
		     *
		     * function duplicate(n) {
		     *   return [n, n];
		     * }
		     *
		     * _.flatMap([1, 2], duplicate);
		     * // => [1, 1, 2, 2]
		     */
		    function flatMap(collection, iteratee) {
		      return baseFlatten(map(collection, iteratee), 1);
		    }

		    /**
		     * This method is like `_.flatMap` except that it recursively flattens the
		     * mapped results.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.7.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the new flattened array.
		     * @example
		     *
		     * function duplicate(n) {
		     *   return [[[n, n]]];
		     * }
		     *
		     * _.flatMapDeep([1, 2], duplicate);
		     * // => [1, 1, 2, 2]
		     */
		    function flatMapDeep(collection, iteratee) {
		      return baseFlatten(map(collection, iteratee), INFINITY);
		    }

		    /**
		     * This method is like `_.flatMap` except that it recursively flattens the
		     * mapped results up to `depth` times.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.7.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @param {number} [depth=1] The maximum recursion depth.
		     * @returns {Array} Returns the new flattened array.
		     * @example
		     *
		     * function duplicate(n) {
		     *   return [[[n, n]]];
		     * }
		     *
		     * _.flatMapDepth([1, 2], duplicate, 2);
		     * // => [[1, 1], [2, 2]]
		     */
		    function flatMapDepth(collection, iteratee, depth) {
		      depth = depth === undefined$1 ? 1 : toInteger(depth);
		      return baseFlatten(map(collection, iteratee), depth);
		    }

		    /**
		     * Iterates over elements of `collection` and invokes `iteratee` for each element.
		     * The iteratee is invoked with three arguments: (value, index|key, collection).
		     * Iteratee functions may exit iteration early by explicitly returning `false`.
		     *
		     * **Note:** As with other "Collections" methods, objects with a "length"
		     * property are iterated like arrays. To avoid this behavior use `_.forIn`
		     * or `_.forOwn` for object iteration.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @alias each
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Array|Object} Returns `collection`.
		     * @see _.forEachRight
		     * @example
		     *
		     * _.forEach([1, 2], function(value) {
		     *   console.log(value);
		     * });
		     * // => Logs `1` then `2`.
		     *
		     * _.forEach({ 'a': 1, 'b': 2 }, function(value, key) {
		     *   console.log(key);
		     * });
		     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
		     */
		    function forEach(collection, iteratee) {
		      var func = isArray(collection) ? arrayEach : baseEach;
		      return func(collection, getIteratee(iteratee, 3));
		    }

		    /**
		     * This method is like `_.forEach` except that it iterates over elements of
		     * `collection` from right to left.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @alias eachRight
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Array|Object} Returns `collection`.
		     * @see _.forEach
		     * @example
		     *
		     * _.forEachRight([1, 2], function(value) {
		     *   console.log(value);
		     * });
		     * // => Logs `2` then `1`.
		     */
		    function forEachRight(collection, iteratee) {
		      var func = isArray(collection) ? arrayEachRight : baseEachRight;
		      return func(collection, getIteratee(iteratee, 3));
		    }

		    /**
		     * Creates an object composed of keys generated from the results of running
		     * each element of `collection` thru `iteratee`. The order of grouped values
		     * is determined by the order they occur in `collection`. The corresponding
		     * value of each key is an array of elements responsible for generating the
		     * key. The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
		     * @returns {Object} Returns the composed aggregate object.
		     * @example
		     *
		     * _.groupBy([6.1, 4.2, 6.3], Math.floor);
		     * // => { '4': [4.2], '6': [6.1, 6.3] }
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.groupBy(['one', 'two', 'three'], 'length');
		     * // => { '3': ['one', 'two'], '5': ['three'] }
		     */
		    var groupBy = createAggregator(function(result, value, key) {
		      if (hasOwnProperty.call(result, key)) {
		        result[key].push(value);
		      } else {
		        baseAssignValue(result, key, [value]);
		      }
		    });

		    /**
		     * Checks if `value` is in `collection`. If `collection` is a string, it's
		     * checked for a substring of `value`, otherwise
		     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * is used for equality comparisons. If `fromIndex` is negative, it's used as
		     * the offset from the end of `collection`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object|string} collection The collection to inspect.
		     * @param {*} value The value to search for.
		     * @param {number} [fromIndex=0] The index to search from.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
		     * @returns {boolean} Returns `true` if `value` is found, else `false`.
		     * @example
		     *
		     * _.includes([1, 2, 3], 1);
		     * // => true
		     *
		     * _.includes([1, 2, 3], 1, 2);
		     * // => false
		     *
		     * _.includes({ 'a': 1, 'b': 2 }, 1);
		     * // => true
		     *
		     * _.includes('abcd', 'bc');
		     * // => true
		     */
		    function includes(collection, value, fromIndex, guard) {
		      collection = isArrayLike(collection) ? collection : values(collection);
		      fromIndex = (fromIndex && !guard) ? toInteger(fromIndex) : 0;

		      var length = collection.length;
		      if (fromIndex < 0) {
		        fromIndex = nativeMax(length + fromIndex, 0);
		      }
		      return isString(collection)
		        ? (fromIndex <= length && collection.indexOf(value, fromIndex) > -1)
		        : (!!length && baseIndexOf(collection, value, fromIndex) > -1);
		    }

		    /**
		     * Invokes the method at `path` of each element in `collection`, returning
		     * an array of the results of each invoked method. Any additional arguments
		     * are provided to each invoked method. If `path` is a function, it's invoked
		     * for, and `this` bound to, each element in `collection`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Array|Function|string} path The path of the method to invoke or
		     *  the function invoked per iteration.
		     * @param {...*} [args] The arguments to invoke each method with.
		     * @returns {Array} Returns the array of results.
		     * @example
		     *
		     * _.invokeMap([[5, 1, 7], [3, 2, 1]], 'sort');
		     * // => [[1, 5, 7], [1, 2, 3]]
		     *
		     * _.invokeMap([123, 456], String.prototype.split, '');
		     * // => [['1', '2', '3'], ['4', '5', '6']]
		     */
		    var invokeMap = baseRest(function(collection, path, args) {
		      var index = -1,
		          isFunc = typeof path == 'function',
		          result = isArrayLike(collection) ? Array(collection.length) : [];

		      baseEach(collection, function(value) {
		        result[++index] = isFunc ? apply(path, value, args) : baseInvoke(value, path, args);
		      });
		      return result;
		    });

		    /**
		     * Creates an object composed of keys generated from the results of running
		     * each element of `collection` thru `iteratee`. The corresponding value of
		     * each key is the last element responsible for generating the key. The
		     * iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
		     * @returns {Object} Returns the composed aggregate object.
		     * @example
		     *
		     * var array = [
		     *   { 'dir': 'left', 'code': 97 },
		     *   { 'dir': 'right', 'code': 100 }
		     * ];
		     *
		     * _.keyBy(array, function(o) {
		     *   return String.fromCharCode(o.code);
		     * });
		     * // => { 'a': { 'dir': 'left', 'code': 97 }, 'd': { 'dir': 'right', 'code': 100 } }
		     *
		     * _.keyBy(array, 'dir');
		     * // => { 'left': { 'dir': 'left', 'code': 97 }, 'right': { 'dir': 'right', 'code': 100 } }
		     */
		    var keyBy = createAggregator(function(result, value, key) {
		      baseAssignValue(result, key, value);
		    });

		    /**
		     * Creates an array of values by running each element in `collection` thru
		     * `iteratee`. The iteratee is invoked with three arguments:
		     * (value, index|key, collection).
		     *
		     * Many lodash methods are guarded to work as iteratees for methods like
		     * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
		     *
		     * The guarded methods are:
		     * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
		     * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
		     * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
		     * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the new mapped array.
		     * @example
		     *
		     * function square(n) {
		     *   return n * n;
		     * }
		     *
		     * _.map([4, 8], square);
		     * // => [16, 64]
		     *
		     * _.map({ 'a': 4, 'b': 8 }, square);
		     * // => [16, 64] (iteration order is not guaranteed)
		     *
		     * var users = [
		     *   { 'user': 'barney' },
		     *   { 'user': 'fred' }
		     * ];
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.map(users, 'user');
		     * // => ['barney', 'fred']
		     */
		    function map(collection, iteratee) {
		      var func = isArray(collection) ? arrayMap : baseMap;
		      return func(collection, getIteratee(iteratee, 3));
		    }

		    /**
		     * This method is like `_.sortBy` except that it allows specifying the sort
		     * orders of the iteratees to sort by. If `orders` is unspecified, all values
		     * are sorted in ascending order. Otherwise, specify an order of "desc" for
		     * descending or "asc" for ascending sort order of corresponding values.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Array[]|Function[]|Object[]|string[]} [iteratees=[_.identity]]
		     *  The iteratees to sort by.
		     * @param {string[]} [orders] The sort orders of `iteratees`.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
		     * @returns {Array} Returns the new sorted array.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'fred',   'age': 48 },
		     *   { 'user': 'barney', 'age': 34 },
		     *   { 'user': 'fred',   'age': 40 },
		     *   { 'user': 'barney', 'age': 36 }
		     * ];
		     *
		     * // Sort by `user` in ascending order and by `age` in descending order.
		     * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
		     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
		     */
		    function orderBy(collection, iteratees, orders, guard) {
		      if (collection == null) {
		        return [];
		      }
		      if (!isArray(iteratees)) {
		        iteratees = iteratees == null ? [] : [iteratees];
		      }
		      orders = guard ? undefined$1 : orders;
		      if (!isArray(orders)) {
		        orders = orders == null ? [] : [orders];
		      }
		      return baseOrderBy(collection, iteratees, orders);
		    }

		    /**
		     * Creates an array of elements split into two groups, the first of which
		     * contains elements `predicate` returns truthy for, the second of which
		     * contains elements `predicate` returns falsey for. The predicate is
		     * invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the array of grouped elements.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney',  'age': 36, 'active': false },
		     *   { 'user': 'fred',    'age': 40, 'active': true },
		     *   { 'user': 'pebbles', 'age': 1,  'active': false }
		     * ];
		     *
		     * _.partition(users, function(o) { return o.active; });
		     * // => objects for [['fred'], ['barney', 'pebbles']]
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.partition(users, { 'age': 1, 'active': false });
		     * // => objects for [['pebbles'], ['barney', 'fred']]
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.partition(users, ['active', false]);
		     * // => objects for [['barney', 'pebbles'], ['fred']]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.partition(users, 'active');
		     * // => objects for [['fred'], ['barney', 'pebbles']]
		     */
		    var partition = createAggregator(function(result, value, key) {
		      result[key ? 0 : 1].push(value);
		    }, function() { return [[], []]; });

		    /**
		     * Reduces `collection` to a value which is the accumulated result of running
		     * each element in `collection` thru `iteratee`, where each successive
		     * invocation is supplied the return value of the previous. If `accumulator`
		     * is not given, the first element of `collection` is used as the initial
		     * value. The iteratee is invoked with four arguments:
		     * (accumulator, value, index|key, collection).
		     *
		     * Many lodash methods are guarded to work as iteratees for methods like
		     * `_.reduce`, `_.reduceRight`, and `_.transform`.
		     *
		     * The guarded methods are:
		     * `assign`, `defaults`, `defaultsDeep`, `includes`, `merge`, `orderBy`,
		     * and `sortBy`
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @param {*} [accumulator] The initial value.
		     * @returns {*} Returns the accumulated value.
		     * @see _.reduceRight
		     * @example
		     *
		     * _.reduce([1, 2], function(sum, n) {
		     *   return sum + n;
		     * }, 0);
		     * // => 3
		     *
		     * _.reduce({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
		     *   (result[value] || (result[value] = [])).push(key);
		     *   return result;
		     * }, {});
		     * // => { '1': ['a', 'c'], '2': ['b'] } (iteration order is not guaranteed)
		     */
		    function reduce(collection, iteratee, accumulator) {
		      var func = isArray(collection) ? arrayReduce : baseReduce,
		          initAccum = arguments.length < 3;

		      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEach);
		    }

		    /**
		     * This method is like `_.reduce` except that it iterates over elements of
		     * `collection` from right to left.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @param {*} [accumulator] The initial value.
		     * @returns {*} Returns the accumulated value.
		     * @see _.reduce
		     * @example
		     *
		     * var array = [[0, 1], [2, 3], [4, 5]];
		     *
		     * _.reduceRight(array, function(flattened, other) {
		     *   return flattened.concat(other);
		     * }, []);
		     * // => [4, 5, 2, 3, 0, 1]
		     */
		    function reduceRight(collection, iteratee, accumulator) {
		      var func = isArray(collection) ? arrayReduceRight : baseReduce,
		          initAccum = arguments.length < 3;

		      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEachRight);
		    }

		    /**
		     * The opposite of `_.filter`; this method returns the elements of `collection`
		     * that `predicate` does **not** return truthy for.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the new filtered array.
		     * @see _.filter
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney', 'age': 36, 'active': false },
		     *   { 'user': 'fred',   'age': 40, 'active': true }
		     * ];
		     *
		     * _.reject(users, function(o) { return !o.active; });
		     * // => objects for ['fred']
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.reject(users, { 'age': 40, 'active': true });
		     * // => objects for ['barney']
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.reject(users, ['active', false]);
		     * // => objects for ['fred']
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.reject(users, 'active');
		     * // => objects for ['barney']
		     */
		    function reject(collection, predicate) {
		      var func = isArray(collection) ? arrayFilter : baseFilter;
		      return func(collection, negate(getIteratee(predicate, 3)));
		    }

		    /**
		     * Gets a random element from `collection`.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to sample.
		     * @returns {*} Returns the random element.
		     * @example
		     *
		     * _.sample([1, 2, 3, 4]);
		     * // => 2
		     */
		    function sample(collection) {
		      var func = isArray(collection) ? arraySample : baseSample;
		      return func(collection);
		    }

		    /**
		     * Gets `n` random elements at unique keys from `collection` up to the
		     * size of `collection`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to sample.
		     * @param {number} [n=1] The number of elements to sample.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the random elements.
		     * @example
		     *
		     * _.sampleSize([1, 2, 3], 2);
		     * // => [3, 1]
		     *
		     * _.sampleSize([1, 2, 3], 4);
		     * // => [2, 3, 1]
		     */
		    function sampleSize(collection, n, guard) {
		      if ((guard ? isIterateeCall(collection, n, guard) : n === undefined$1)) {
		        n = 1;
		      } else {
		        n = toInteger(n);
		      }
		      var func = isArray(collection) ? arraySampleSize : baseSampleSize;
		      return func(collection, n);
		    }

		    /**
		     * Creates an array of shuffled values, using a version of the
		     * [Fisher-Yates shuffle](https://en.wikipedia.org/wiki/Fisher-Yates_shuffle).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to shuffle.
		     * @returns {Array} Returns the new shuffled array.
		     * @example
		     *
		     * _.shuffle([1, 2, 3, 4]);
		     * // => [4, 1, 3, 2]
		     */
		    function shuffle(collection) {
		      var func = isArray(collection) ? arrayShuffle : baseShuffle;
		      return func(collection);
		    }

		    /**
		     * Gets the size of `collection` by returning its length for array-like
		     * values or the number of own enumerable string keyed properties for objects.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object|string} collection The collection to inspect.
		     * @returns {number} Returns the collection size.
		     * @example
		     *
		     * _.size([1, 2, 3]);
		     * // => 3
		     *
		     * _.size({ 'a': 1, 'b': 2 });
		     * // => 2
		     *
		     * _.size('pebbles');
		     * // => 7
		     */
		    function size(collection) {
		      if (collection == null) {
		        return 0;
		      }
		      if (isArrayLike(collection)) {
		        return isString(collection) ? stringSize(collection) : collection.length;
		      }
		      var tag = getTag(collection);
		      if (tag == mapTag || tag == setTag) {
		        return collection.size;
		      }
		      return baseKeys(collection).length;
		    }

		    /**
		     * Checks if `predicate` returns truthy for **any** element of `collection`.
		     * Iteration is stopped once `predicate` returns truthy. The predicate is
		     * invoked with three arguments: (value, index|key, collection).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {boolean} Returns `true` if any element passes the predicate check,
		     *  else `false`.
		     * @example
		     *
		     * _.some([null, 0, 'yes', false], Boolean);
		     * // => true
		     *
		     * var users = [
		     *   { 'user': 'barney', 'active': true },
		     *   { 'user': 'fred',   'active': false }
		     * ];
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.some(users, { 'user': 'barney', 'active': false });
		     * // => false
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.some(users, ['active', false]);
		     * // => true
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.some(users, 'active');
		     * // => true
		     */
		    function some(collection, predicate, guard) {
		      var func = isArray(collection) ? arraySome : baseSome;
		      if (guard && isIterateeCall(collection, predicate, guard)) {
		        predicate = undefined$1;
		      }
		      return func(collection, getIteratee(predicate, 3));
		    }

		    /**
		     * Creates an array of elements, sorted in ascending order by the results of
		     * running each element in a collection thru each iteratee. This method
		     * performs a stable sort, that is, it preserves the original sort order of
		     * equal elements. The iteratees are invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Collection
		     * @param {Array|Object} collection The collection to iterate over.
		     * @param {...(Function|Function[])} [iteratees=[_.identity]]
		     *  The iteratees to sort by.
		     * @returns {Array} Returns the new sorted array.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'fred',   'age': 48 },
		     *   { 'user': 'barney', 'age': 36 },
		     *   { 'user': 'fred',   'age': 30 },
		     *   { 'user': 'barney', 'age': 34 }
		     * ];
		     *
		     * _.sortBy(users, [function(o) { return o.user; }]);
		     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
		     *
		     * _.sortBy(users, ['user', 'age']);
		     * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
		     */
		    var sortBy = baseRest(function(collection, iteratees) {
		      if (collection == null) {
		        return [];
		      }
		      var length = iteratees.length;
		      if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
		        iteratees = [];
		      } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
		        iteratees = [iteratees[0]];
		      }
		      return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
		    });

		    /*------------------------------------------------------------------------*/

		    /**
		     * Gets the timestamp of the number of milliseconds that have elapsed since
		     * the Unix epoch (1 January 1970 00:00:00 UTC).
		     *
		     * @static
		     * @memberOf _
		     * @since 2.4.0
		     * @category Date
		     * @returns {number} Returns the timestamp.
		     * @example
		     *
		     * _.defer(function(stamp) {
		     *   console.log(_.now() - stamp);
		     * }, _.now());
		     * // => Logs the number of milliseconds it took for the deferred invocation.
		     */
		    var now = ctxNow || function() {
		      return root.Date.now();
		    };

		    /*------------------------------------------------------------------------*/

		    /**
		     * The opposite of `_.before`; this method creates a function that invokes
		     * `func` once it's called `n` or more times.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {number} n The number of calls before `func` is invoked.
		     * @param {Function} func The function to restrict.
		     * @returns {Function} Returns the new restricted function.
		     * @example
		     *
		     * var saves = ['profile', 'settings'];
		     *
		     * var done = _.after(saves.length, function() {
		     *   console.log('done saving!');
		     * });
		     *
		     * _.forEach(saves, function(type) {
		     *   asyncSave({ 'type': type, 'complete': done });
		     * });
		     * // => Logs 'done saving!' after the two async saves have completed.
		     */
		    function after(n, func) {
		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      n = toInteger(n);
		      return function() {
		        if (--n < 1) {
		          return func.apply(this, arguments);
		        }
		      };
		    }

		    /**
		     * Creates a function that invokes `func`, with up to `n` arguments,
		     * ignoring any additional arguments.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Function
		     * @param {Function} func The function to cap arguments for.
		     * @param {number} [n=func.length] The arity cap.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Function} Returns the new capped function.
		     * @example
		     *
		     * _.map(['6', '8', '10'], _.ary(parseInt, 1));
		     * // => [6, 8, 10]
		     */
		    function ary(func, n, guard) {
		      n = guard ? undefined$1 : n;
		      n = (func && n == null) ? func.length : n;
		      return createWrap(func, WRAP_ARY_FLAG, undefined$1, undefined$1, undefined$1, undefined$1, n);
		    }

		    /**
		     * Creates a function that invokes `func`, with the `this` binding and arguments
		     * of the created function, while it's called less than `n` times. Subsequent
		     * calls to the created function return the result of the last `func` invocation.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Function
		     * @param {number} n The number of calls at which `func` is no longer invoked.
		     * @param {Function} func The function to restrict.
		     * @returns {Function} Returns the new restricted function.
		     * @example
		     *
		     * jQuery(element).on('click', _.before(5, addContactToList));
		     * // => Allows adding up to 4 contacts to the list.
		     */
		    function before(n, func) {
		      var result;
		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      n = toInteger(n);
		      return function() {
		        if (--n > 0) {
		          result = func.apply(this, arguments);
		        }
		        if (n <= 1) {
		          func = undefined$1;
		        }
		        return result;
		      };
		    }

		    /**
		     * Creates a function that invokes `func` with the `this` binding of `thisArg`
		     * and `partials` prepended to the arguments it receives.
		     *
		     * The `_.bind.placeholder` value, which defaults to `_` in monolithic builds,
		     * may be used as a placeholder for partially applied arguments.
		     *
		     * **Note:** Unlike native `Function#bind`, this method doesn't set the "length"
		     * property of bound functions.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to bind.
		     * @param {*} thisArg The `this` binding of `func`.
		     * @param {...*} [partials] The arguments to be partially applied.
		     * @returns {Function} Returns the new bound function.
		     * @example
		     *
		     * function greet(greeting, punctuation) {
		     *   return greeting + ' ' + this.user + punctuation;
		     * }
		     *
		     * var object = { 'user': 'fred' };
		     *
		     * var bound = _.bind(greet, object, 'hi');
		     * bound('!');
		     * // => 'hi fred!'
		     *
		     * // Bound with placeholders.
		     * var bound = _.bind(greet, object, _, '!');
		     * bound('hi');
		     * // => 'hi fred!'
		     */
		    var bind = baseRest(function(func, thisArg, partials) {
		      var bitmask = WRAP_BIND_FLAG;
		      if (partials.length) {
		        var holders = replaceHolders(partials, getHolder(bind));
		        bitmask |= WRAP_PARTIAL_FLAG;
		      }
		      return createWrap(func, bitmask, thisArg, partials, holders);
		    });

		    /**
		     * Creates a function that invokes the method at `object[key]` with `partials`
		     * prepended to the arguments it receives.
		     *
		     * This method differs from `_.bind` by allowing bound functions to reference
		     * methods that may be redefined or don't yet exist. See
		     * [Peter Michaux's article](http://peter.michaux.ca/articles/lazy-function-definition-pattern)
		     * for more details.
		     *
		     * The `_.bindKey.placeholder` value, which defaults to `_` in monolithic
		     * builds, may be used as a placeholder for partially applied arguments.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.10.0
		     * @category Function
		     * @param {Object} object The object to invoke the method on.
		     * @param {string} key The key of the method.
		     * @param {...*} [partials] The arguments to be partially applied.
		     * @returns {Function} Returns the new bound function.
		     * @example
		     *
		     * var object = {
		     *   'user': 'fred',
		     *   'greet': function(greeting, punctuation) {
		     *     return greeting + ' ' + this.user + punctuation;
		     *   }
		     * };
		     *
		     * var bound = _.bindKey(object, 'greet', 'hi');
		     * bound('!');
		     * // => 'hi fred!'
		     *
		     * object.greet = function(greeting, punctuation) {
		     *   return greeting + 'ya ' + this.user + punctuation;
		     * };
		     *
		     * bound('!');
		     * // => 'hiya fred!'
		     *
		     * // Bound with placeholders.
		     * var bound = _.bindKey(object, 'greet', _, '!');
		     * bound('hi');
		     * // => 'hiya fred!'
		     */
		    var bindKey = baseRest(function(object, key, partials) {
		      var bitmask = WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG;
		      if (partials.length) {
		        var holders = replaceHolders(partials, getHolder(bindKey));
		        bitmask |= WRAP_PARTIAL_FLAG;
		      }
		      return createWrap(key, bitmask, object, partials, holders);
		    });

		    /**
		     * Creates a function that accepts arguments of `func` and either invokes
		     * `func` returning its result, if at least `arity` number of arguments have
		     * been provided, or returns a function that accepts the remaining `func`
		     * arguments, and so on. The arity of `func` may be specified if `func.length`
		     * is not sufficient.
		     *
		     * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
		     * may be used as a placeholder for provided arguments.
		     *
		     * **Note:** This method doesn't set the "length" property of curried functions.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Function
		     * @param {Function} func The function to curry.
		     * @param {number} [arity=func.length] The arity of `func`.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Function} Returns the new curried function.
		     * @example
		     *
		     * var abc = function(a, b, c) {
		     *   return [a, b, c];
		     * };
		     *
		     * var curried = _.curry(abc);
		     *
		     * curried(1)(2)(3);
		     * // => [1, 2, 3]
		     *
		     * curried(1, 2)(3);
		     * // => [1, 2, 3]
		     *
		     * curried(1, 2, 3);
		     * // => [1, 2, 3]
		     *
		     * // Curried with placeholders.
		     * curried(1)(_, 3)(2);
		     * // => [1, 2, 3]
		     */
		    function curry(func, arity, guard) {
		      arity = guard ? undefined$1 : arity;
		      var result = createWrap(func, WRAP_CURRY_FLAG, undefined$1, undefined$1, undefined$1, undefined$1, undefined$1, arity);
		      result.placeholder = curry.placeholder;
		      return result;
		    }

		    /**
		     * This method is like `_.curry` except that arguments are applied to `func`
		     * in the manner of `_.partialRight` instead of `_.partial`.
		     *
		     * The `_.curryRight.placeholder` value, which defaults to `_` in monolithic
		     * builds, may be used as a placeholder for provided arguments.
		     *
		     * **Note:** This method doesn't set the "length" property of curried functions.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Function
		     * @param {Function} func The function to curry.
		     * @param {number} [arity=func.length] The arity of `func`.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Function} Returns the new curried function.
		     * @example
		     *
		     * var abc = function(a, b, c) {
		     *   return [a, b, c];
		     * };
		     *
		     * var curried = _.curryRight(abc);
		     *
		     * curried(3)(2)(1);
		     * // => [1, 2, 3]
		     *
		     * curried(2, 3)(1);
		     * // => [1, 2, 3]
		     *
		     * curried(1, 2, 3);
		     * // => [1, 2, 3]
		     *
		     * // Curried with placeholders.
		     * curried(3)(1, _)(2);
		     * // => [1, 2, 3]
		     */
		    function curryRight(func, arity, guard) {
		      arity = guard ? undefined$1 : arity;
		      var result = createWrap(func, WRAP_CURRY_RIGHT_FLAG, undefined$1, undefined$1, undefined$1, undefined$1, undefined$1, arity);
		      result.placeholder = curryRight.placeholder;
		      return result;
		    }

		    /**
		     * Creates a debounced function that delays invoking `func` until after `wait`
		     * milliseconds have elapsed since the last time the debounced function was
		     * invoked. The debounced function comes with a `cancel` method to cancel
		     * delayed `func` invocations and a `flush` method to immediately invoke them.
		     * Provide `options` to indicate whether `func` should be invoked on the
		     * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
		     * with the last arguments provided to the debounced function. Subsequent
		     * calls to the debounced function return the result of the last `func`
		     * invocation.
		     *
		     * **Note:** If `leading` and `trailing` options are `true`, `func` is
		     * invoked on the trailing edge of the timeout only if the debounced function
		     * is invoked more than once during the `wait` timeout.
		     *
		     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
		     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
		     *
		     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
		     * for details over the differences between `_.debounce` and `_.throttle`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to debounce.
		     * @param {number} [wait=0] The number of milliseconds to delay.
		     * @param {Object} [options={}] The options object.
		     * @param {boolean} [options.leading=false]
		     *  Specify invoking on the leading edge of the timeout.
		     * @param {number} [options.maxWait]
		     *  The maximum time `func` is allowed to be delayed before it's invoked.
		     * @param {boolean} [options.trailing=true]
		     *  Specify invoking on the trailing edge of the timeout.
		     * @returns {Function} Returns the new debounced function.
		     * @example
		     *
		     * // Avoid costly calculations while the window size is in flux.
		     * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
		     *
		     * // Invoke `sendMail` when clicked, debouncing subsequent calls.
		     * jQuery(element).on('click', _.debounce(sendMail, 300, {
		     *   'leading': true,
		     *   'trailing': false
		     * }));
		     *
		     * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
		     * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
		     * var source = new EventSource('/stream');
		     * jQuery(source).on('message', debounced);
		     *
		     * // Cancel the trailing debounced invocation.
		     * jQuery(window).on('popstate', debounced.cancel);
		     */
		    function debounce(func, wait, options) {
		      var lastArgs,
		          lastThis,
		          maxWait,
		          result,
		          timerId,
		          lastCallTime,
		          lastInvokeTime = 0,
		          leading = false,
		          maxing = false,
		          trailing = true;

		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      wait = toNumber(wait) || 0;
		      if (isObject(options)) {
		        leading = !!options.leading;
		        maxing = 'maxWait' in options;
		        maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
		        trailing = 'trailing' in options ? !!options.trailing : trailing;
		      }

		      function invokeFunc(time) {
		        var args = lastArgs,
		            thisArg = lastThis;

		        lastArgs = lastThis = undefined$1;
		        lastInvokeTime = time;
		        result = func.apply(thisArg, args);
		        return result;
		      }

		      function leadingEdge(time) {
		        // Reset any `maxWait` timer.
		        lastInvokeTime = time;
		        // Start the timer for the trailing edge.
		        timerId = setTimeout(timerExpired, wait);
		        // Invoke the leading edge.
		        return leading ? invokeFunc(time) : result;
		      }

		      function remainingWait(time) {
		        var timeSinceLastCall = time - lastCallTime,
		            timeSinceLastInvoke = time - lastInvokeTime,
		            timeWaiting = wait - timeSinceLastCall;

		        return maxing
		          ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
		          : timeWaiting;
		      }

		      function shouldInvoke(time) {
		        var timeSinceLastCall = time - lastCallTime,
		            timeSinceLastInvoke = time - lastInvokeTime;

		        // Either this is the first call, activity has stopped and we're at the
		        // trailing edge, the system time has gone backwards and we're treating
		        // it as the trailing edge, or we've hit the `maxWait` limit.
		        return (lastCallTime === undefined$1 || (timeSinceLastCall >= wait) ||
		          (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
		      }

		      function timerExpired() {
		        var time = now();
		        if (shouldInvoke(time)) {
		          return trailingEdge(time);
		        }
		        // Restart the timer.
		        timerId = setTimeout(timerExpired, remainingWait(time));
		      }

		      function trailingEdge(time) {
		        timerId = undefined$1;

		        // Only invoke if we have `lastArgs` which means `func` has been
		        // debounced at least once.
		        if (trailing && lastArgs) {
		          return invokeFunc(time);
		        }
		        lastArgs = lastThis = undefined$1;
		        return result;
		      }

		      function cancel() {
		        if (timerId !== undefined$1) {
		          clearTimeout(timerId);
		        }
		        lastInvokeTime = 0;
		        lastArgs = lastCallTime = lastThis = timerId = undefined$1;
		      }

		      function flush() {
		        return timerId === undefined$1 ? result : trailingEdge(now());
		      }

		      function debounced() {
		        var time = now(),
		            isInvoking = shouldInvoke(time);

		        lastArgs = arguments;
		        lastThis = this;
		        lastCallTime = time;

		        if (isInvoking) {
		          if (timerId === undefined$1) {
		            return leadingEdge(lastCallTime);
		          }
		          if (maxing) {
		            // Handle invocations in a tight loop.
		            clearTimeout(timerId);
		            timerId = setTimeout(timerExpired, wait);
		            return invokeFunc(lastCallTime);
		          }
		        }
		        if (timerId === undefined$1) {
		          timerId = setTimeout(timerExpired, wait);
		        }
		        return result;
		      }
		      debounced.cancel = cancel;
		      debounced.flush = flush;
		      return debounced;
		    }

		    /**
		     * Defers invoking the `func` until the current call stack has cleared. Any
		     * additional arguments are provided to `func` when it's invoked.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to defer.
		     * @param {...*} [args] The arguments to invoke `func` with.
		     * @returns {number} Returns the timer id.
		     * @example
		     *
		     * _.defer(function(text) {
		     *   console.log(text);
		     * }, 'deferred');
		     * // => Logs 'deferred' after one millisecond.
		     */
		    var defer = baseRest(function(func, args) {
		      return baseDelay(func, 1, args);
		    });

		    /**
		     * Invokes `func` after `wait` milliseconds. Any additional arguments are
		     * provided to `func` when it's invoked.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to delay.
		     * @param {number} wait The number of milliseconds to delay invocation.
		     * @param {...*} [args] The arguments to invoke `func` with.
		     * @returns {number} Returns the timer id.
		     * @example
		     *
		     * _.delay(function(text) {
		     *   console.log(text);
		     * }, 1000, 'later');
		     * // => Logs 'later' after one second.
		     */
		    var delay = baseRest(function(func, wait, args) {
		      return baseDelay(func, toNumber(wait) || 0, args);
		    });

		    /**
		     * Creates a function that invokes `func` with arguments reversed.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Function
		     * @param {Function} func The function to flip arguments for.
		     * @returns {Function} Returns the new flipped function.
		     * @example
		     *
		     * var flipped = _.flip(function() {
		     *   return _.toArray(arguments);
		     * });
		     *
		     * flipped('a', 'b', 'c', 'd');
		     * // => ['d', 'c', 'b', 'a']
		     */
		    function flip(func) {
		      return createWrap(func, WRAP_FLIP_FLAG);
		    }

		    /**
		     * Creates a function that memoizes the result of `func`. If `resolver` is
		     * provided, it determines the cache key for storing the result based on the
		     * arguments provided to the memoized function. By default, the first argument
		     * provided to the memoized function is used as the map cache key. The `func`
		     * is invoked with the `this` binding of the memoized function.
		     *
		     * **Note:** The cache is exposed as the `cache` property on the memoized
		     * function. Its creation may be customized by replacing the `_.memoize.Cache`
		     * constructor with one whose instances implement the
		     * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
		     * method interface of `clear`, `delete`, `get`, `has`, and `set`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to have its output memoized.
		     * @param {Function} [resolver] The function to resolve the cache key.
		     * @returns {Function} Returns the new memoized function.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': 2 };
		     * var other = { 'c': 3, 'd': 4 };
		     *
		     * var values = _.memoize(_.values);
		     * values(object);
		     * // => [1, 2]
		     *
		     * values(other);
		     * // => [3, 4]
		     *
		     * object.a = 2;
		     * values(object);
		     * // => [1, 2]
		     *
		     * // Modify the result cache.
		     * values.cache.set(object, ['a', 'b']);
		     * values(object);
		     * // => ['a', 'b']
		     *
		     * // Replace `_.memoize.Cache`.
		     * _.memoize.Cache = WeakMap;
		     */
		    function memoize(func, resolver) {
		      if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      var memoized = function() {
		        var args = arguments,
		            key = resolver ? resolver.apply(this, args) : args[0],
		            cache = memoized.cache;

		        if (cache.has(key)) {
		          return cache.get(key);
		        }
		        var result = func.apply(this, args);
		        memoized.cache = cache.set(key, result) || cache;
		        return result;
		      };
		      memoized.cache = new (memoize.Cache || MapCache);
		      return memoized;
		    }

		    // Expose `MapCache`.
		    memoize.Cache = MapCache;

		    /**
		     * Creates a function that negates the result of the predicate `func`. The
		     * `func` predicate is invoked with the `this` binding and arguments of the
		     * created function.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Function
		     * @param {Function} predicate The predicate to negate.
		     * @returns {Function} Returns the new negated function.
		     * @example
		     *
		     * function isEven(n) {
		     *   return n % 2 == 0;
		     * }
		     *
		     * _.filter([1, 2, 3, 4, 5, 6], _.negate(isEven));
		     * // => [1, 3, 5]
		     */
		    function negate(predicate) {
		      if (typeof predicate != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      return function() {
		        var args = arguments;
		        switch (args.length) {
		          case 0: return !predicate.call(this);
		          case 1: return !predicate.call(this, args[0]);
		          case 2: return !predicate.call(this, args[0], args[1]);
		          case 3: return !predicate.call(this, args[0], args[1], args[2]);
		        }
		        return !predicate.apply(this, args);
		      };
		    }

		    /**
		     * Creates a function that is restricted to invoking `func` once. Repeat calls
		     * to the function return the value of the first invocation. The `func` is
		     * invoked with the `this` binding and arguments of the created function.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to restrict.
		     * @returns {Function} Returns the new restricted function.
		     * @example
		     *
		     * var initialize = _.once(createApplication);
		     * initialize();
		     * initialize();
		     * // => `createApplication` is invoked once
		     */
		    function once(func) {
		      return before(2, func);
		    }

		    /**
		     * Creates a function that invokes `func` with its arguments transformed.
		     *
		     * @static
		     * @since 4.0.0
		     * @memberOf _
		     * @category Function
		     * @param {Function} func The function to wrap.
		     * @param {...(Function|Function[])} [transforms=[_.identity]]
		     *  The argument transforms.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * function doubled(n) {
		     *   return n * 2;
		     * }
		     *
		     * function square(n) {
		     *   return n * n;
		     * }
		     *
		     * var func = _.overArgs(function(x, y) {
		     *   return [x, y];
		     * }, [square, doubled]);
		     *
		     * func(9, 3);
		     * // => [81, 6]
		     *
		     * func(10, 5);
		     * // => [100, 10]
		     */
		    var overArgs = castRest(function(func, transforms) {
		      transforms = (transforms.length == 1 && isArray(transforms[0]))
		        ? arrayMap(transforms[0], baseUnary(getIteratee()))
		        : arrayMap(baseFlatten(transforms, 1), baseUnary(getIteratee()));

		      var funcsLength = transforms.length;
		      return baseRest(function(args) {
		        var index = -1,
		            length = nativeMin(args.length, funcsLength);

		        while (++index < length) {
		          args[index] = transforms[index].call(this, args[index]);
		        }
		        return apply(func, this, args);
		      });
		    });

		    /**
		     * Creates a function that invokes `func` with `partials` prepended to the
		     * arguments it receives. This method is like `_.bind` except it does **not**
		     * alter the `this` binding.
		     *
		     * The `_.partial.placeholder` value, which defaults to `_` in monolithic
		     * builds, may be used as a placeholder for partially applied arguments.
		     *
		     * **Note:** This method doesn't set the "length" property of partially
		     * applied functions.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.2.0
		     * @category Function
		     * @param {Function} func The function to partially apply arguments to.
		     * @param {...*} [partials] The arguments to be partially applied.
		     * @returns {Function} Returns the new partially applied function.
		     * @example
		     *
		     * function greet(greeting, name) {
		     *   return greeting + ' ' + name;
		     * }
		     *
		     * var sayHelloTo = _.partial(greet, 'hello');
		     * sayHelloTo('fred');
		     * // => 'hello fred'
		     *
		     * // Partially applied with placeholders.
		     * var greetFred = _.partial(greet, _, 'fred');
		     * greetFred('hi');
		     * // => 'hi fred'
		     */
		    var partial = baseRest(function(func, partials) {
		      var holders = replaceHolders(partials, getHolder(partial));
		      return createWrap(func, WRAP_PARTIAL_FLAG, undefined$1, partials, holders);
		    });

		    /**
		     * This method is like `_.partial` except that partially applied arguments
		     * are appended to the arguments it receives.
		     *
		     * The `_.partialRight.placeholder` value, which defaults to `_` in monolithic
		     * builds, may be used as a placeholder for partially applied arguments.
		     *
		     * **Note:** This method doesn't set the "length" property of partially
		     * applied functions.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.0.0
		     * @category Function
		     * @param {Function} func The function to partially apply arguments to.
		     * @param {...*} [partials] The arguments to be partially applied.
		     * @returns {Function} Returns the new partially applied function.
		     * @example
		     *
		     * function greet(greeting, name) {
		     *   return greeting + ' ' + name;
		     * }
		     *
		     * var greetFred = _.partialRight(greet, 'fred');
		     * greetFred('hi');
		     * // => 'hi fred'
		     *
		     * // Partially applied with placeholders.
		     * var sayHelloTo = _.partialRight(greet, 'hello', _);
		     * sayHelloTo('fred');
		     * // => 'hello fred'
		     */
		    var partialRight = baseRest(function(func, partials) {
		      var holders = replaceHolders(partials, getHolder(partialRight));
		      return createWrap(func, WRAP_PARTIAL_RIGHT_FLAG, undefined$1, partials, holders);
		    });

		    /**
		     * Creates a function that invokes `func` with arguments arranged according
		     * to the specified `indexes` where the argument value at the first index is
		     * provided as the first argument, the argument value at the second index is
		     * provided as the second argument, and so on.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Function
		     * @param {Function} func The function to rearrange arguments for.
		     * @param {...(number|number[])} indexes The arranged argument indexes.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var rearged = _.rearg(function(a, b, c) {
		     *   return [a, b, c];
		     * }, [2, 0, 1]);
		     *
		     * rearged('b', 'c', 'a')
		     * // => ['a', 'b', 'c']
		     */
		    var rearg = flatRest(function(func, indexes) {
		      return createWrap(func, WRAP_REARG_FLAG, undefined$1, undefined$1, undefined$1, indexes);
		    });

		    /**
		     * Creates a function that invokes `func` with the `this` binding of the
		     * created function and arguments from `start` and beyond provided as
		     * an array.
		     *
		     * **Note:** This method is based on the
		     * [rest parameter](https://mdn.io/rest_parameters).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Function
		     * @param {Function} func The function to apply a rest parameter to.
		     * @param {number} [start=func.length-1] The start position of the rest parameter.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var say = _.rest(function(what, names) {
		     *   return what + ' ' + _.initial(names).join(', ') +
		     *     (_.size(names) > 1 ? ', & ' : '') + _.last(names);
		     * });
		     *
		     * say('hello', 'fred', 'barney', 'pebbles');
		     * // => 'hello fred, barney, & pebbles'
		     */
		    function rest(func, start) {
		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      start = start === undefined$1 ? start : toInteger(start);
		      return baseRest(func, start);
		    }

		    /**
		     * Creates a function that invokes `func` with the `this` binding of the
		     * create function and an array of arguments much like
		     * [`Function#apply`](http://www.ecma-international.org/ecma-262/7.0/#sec-function.prototype.apply).
		     *
		     * **Note:** This method is based on the
		     * [spread operator](https://mdn.io/spread_operator).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.2.0
		     * @category Function
		     * @param {Function} func The function to spread arguments over.
		     * @param {number} [start=0] The start position of the spread.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var say = _.spread(function(who, what) {
		     *   return who + ' says ' + what;
		     * });
		     *
		     * say(['fred', 'hello']);
		     * // => 'fred says hello'
		     *
		     * var numbers = Promise.all([
		     *   Promise.resolve(40),
		     *   Promise.resolve(36)
		     * ]);
		     *
		     * numbers.then(_.spread(function(x, y) {
		     *   return x + y;
		     * }));
		     * // => a Promise of 76
		     */
		    function spread(func, start) {
		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      start = start == null ? 0 : nativeMax(toInteger(start), 0);
		      return baseRest(function(args) {
		        var array = args[start],
		            otherArgs = castSlice(args, 0, start);

		        if (array) {
		          arrayPush(otherArgs, array);
		        }
		        return apply(func, this, otherArgs);
		      });
		    }

		    /**
		     * Creates a throttled function that only invokes `func` at most once per
		     * every `wait` milliseconds. The throttled function comes with a `cancel`
		     * method to cancel delayed `func` invocations and a `flush` method to
		     * immediately invoke them. Provide `options` to indicate whether `func`
		     * should be invoked on the leading and/or trailing edge of the `wait`
		     * timeout. The `func` is invoked with the last arguments provided to the
		     * throttled function. Subsequent calls to the throttled function return the
		     * result of the last `func` invocation.
		     *
		     * **Note:** If `leading` and `trailing` options are `true`, `func` is
		     * invoked on the trailing edge of the timeout only if the throttled function
		     * is invoked more than once during the `wait` timeout.
		     *
		     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
		     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
		     *
		     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
		     * for details over the differences between `_.throttle` and `_.debounce`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {Function} func The function to throttle.
		     * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
		     * @param {Object} [options={}] The options object.
		     * @param {boolean} [options.leading=true]
		     *  Specify invoking on the leading edge of the timeout.
		     * @param {boolean} [options.trailing=true]
		     *  Specify invoking on the trailing edge of the timeout.
		     * @returns {Function} Returns the new throttled function.
		     * @example
		     *
		     * // Avoid excessively updating the position while scrolling.
		     * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
		     *
		     * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
		     * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
		     * jQuery(element).on('click', throttled);
		     *
		     * // Cancel the trailing throttled invocation.
		     * jQuery(window).on('popstate', throttled.cancel);
		     */
		    function throttle(func, wait, options) {
		      var leading = true,
		          trailing = true;

		      if (typeof func != 'function') {
		        throw new TypeError(FUNC_ERROR_TEXT);
		      }
		      if (isObject(options)) {
		        leading = 'leading' in options ? !!options.leading : leading;
		        trailing = 'trailing' in options ? !!options.trailing : trailing;
		      }
		      return debounce(func, wait, {
		        'leading': leading,
		        'maxWait': wait,
		        'trailing': trailing
		      });
		    }

		    /**
		     * Creates a function that accepts up to one argument, ignoring any
		     * additional arguments.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Function
		     * @param {Function} func The function to cap arguments for.
		     * @returns {Function} Returns the new capped function.
		     * @example
		     *
		     * _.map(['6', '8', '10'], _.unary(parseInt));
		     * // => [6, 8, 10]
		     */
		    function unary(func) {
		      return ary(func, 1);
		    }

		    /**
		     * Creates a function that provides `value` to `wrapper` as its first
		     * argument. Any additional arguments provided to the function are appended
		     * to those provided to the `wrapper`. The wrapper is invoked with the `this`
		     * binding of the created function.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Function
		     * @param {*} value The value to wrap.
		     * @param {Function} [wrapper=identity] The wrapper function.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var p = _.wrap(_.escape, function(func, text) {
		     *   return '<p>' + func(text) + '</p>';
		     * });
		     *
		     * p('fred, barney, & pebbles');
		     * // => '<p>fred, barney, &amp; pebbles</p>'
		     */
		    function wrap(value, wrapper) {
		      return partial(castFunction(wrapper), value);
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Casts `value` as an array if it's not one.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.4.0
		     * @category Lang
		     * @param {*} value The value to inspect.
		     * @returns {Array} Returns the cast array.
		     * @example
		     *
		     * _.castArray(1);
		     * // => [1]
		     *
		     * _.castArray({ 'a': 1 });
		     * // => [{ 'a': 1 }]
		     *
		     * _.castArray('abc');
		     * // => ['abc']
		     *
		     * _.castArray(null);
		     * // => [null]
		     *
		     * _.castArray(undefined);
		     * // => [undefined]
		     *
		     * _.castArray();
		     * // => []
		     *
		     * var array = [1, 2, 3];
		     * console.log(_.castArray(array) === array);
		     * // => true
		     */
		    function castArray() {
		      if (!arguments.length) {
		        return [];
		      }
		      var value = arguments[0];
		      return isArray(value) ? value : [value];
		    }

		    /**
		     * Creates a shallow clone of `value`.
		     *
		     * **Note:** This method is loosely based on the
		     * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
		     * and supports cloning arrays, array buffers, booleans, date objects, maps,
		     * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
		     * arrays. The own enumerable properties of `arguments` objects are cloned
		     * as plain objects. An empty object is returned for uncloneable values such
		     * as error objects, functions, DOM nodes, and WeakMaps.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to clone.
		     * @returns {*} Returns the cloned value.
		     * @see _.cloneDeep
		     * @example
		     *
		     * var objects = [{ 'a': 1 }, { 'b': 2 }];
		     *
		     * var shallow = _.clone(objects);
		     * console.log(shallow[0] === objects[0]);
		     * // => true
		     */
		    function clone(value) {
		      return baseClone(value, CLONE_SYMBOLS_FLAG);
		    }

		    /**
		     * This method is like `_.clone` except that it accepts `customizer` which
		     * is invoked to produce the cloned value. If `customizer` returns `undefined`,
		     * cloning is handled by the method instead. The `customizer` is invoked with
		     * up to four arguments; (value [, index|key, object, stack]).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to clone.
		     * @param {Function} [customizer] The function to customize cloning.
		     * @returns {*} Returns the cloned value.
		     * @see _.cloneDeepWith
		     * @example
		     *
		     * function customizer(value) {
		     *   if (_.isElement(value)) {
		     *     return value.cloneNode(false);
		     *   }
		     * }
		     *
		     * var el = _.cloneWith(document.body, customizer);
		     *
		     * console.log(el === document.body);
		     * // => false
		     * console.log(el.nodeName);
		     * // => 'BODY'
		     * console.log(el.childNodes.length);
		     * // => 0
		     */
		    function cloneWith(value, customizer) {
		      customizer = typeof customizer == 'function' ? customizer : undefined$1;
		      return baseClone(value, CLONE_SYMBOLS_FLAG, customizer);
		    }

		    /**
		     * This method is like `_.clone` except that it recursively clones `value`.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.0.0
		     * @category Lang
		     * @param {*} value The value to recursively clone.
		     * @returns {*} Returns the deep cloned value.
		     * @see _.clone
		     * @example
		     *
		     * var objects = [{ 'a': 1 }, { 'b': 2 }];
		     *
		     * var deep = _.cloneDeep(objects);
		     * console.log(deep[0] === objects[0]);
		     * // => false
		     */
		    function cloneDeep(value) {
		      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG);
		    }

		    /**
		     * This method is like `_.cloneWith` except that it recursively clones `value`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to recursively clone.
		     * @param {Function} [customizer] The function to customize cloning.
		     * @returns {*} Returns the deep cloned value.
		     * @see _.cloneWith
		     * @example
		     *
		     * function customizer(value) {
		     *   if (_.isElement(value)) {
		     *     return value.cloneNode(true);
		     *   }
		     * }
		     *
		     * var el = _.cloneDeepWith(document.body, customizer);
		     *
		     * console.log(el === document.body);
		     * // => false
		     * console.log(el.nodeName);
		     * // => 'BODY'
		     * console.log(el.childNodes.length);
		     * // => 20
		     */
		    function cloneDeepWith(value, customizer) {
		      customizer = typeof customizer == 'function' ? customizer : undefined$1;
		      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG, customizer);
		    }

		    /**
		     * Checks if `object` conforms to `source` by invoking the predicate
		     * properties of `source` with the corresponding property values of `object`.
		     *
		     * **Note:** This method is equivalent to `_.conforms` when `source` is
		     * partially applied.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.14.0
		     * @category Lang
		     * @param {Object} object The object to inspect.
		     * @param {Object} source The object of property predicates to conform to.
		     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': 2 };
		     *
		     * _.conformsTo(object, { 'b': function(n) { return n > 1; } });
		     * // => true
		     *
		     * _.conformsTo(object, { 'b': function(n) { return n > 2; } });
		     * // => false
		     */
		    function conformsTo(object, source) {
		      return source == null || baseConformsTo(object, source, keys(source));
		    }

		    /**
		     * Performs a
		     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
		     * comparison between two values to determine if they are equivalent.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
		     * @example
		     *
		     * var object = { 'a': 1 };
		     * var other = { 'a': 1 };
		     *
		     * _.eq(object, object);
		     * // => true
		     *
		     * _.eq(object, other);
		     * // => false
		     *
		     * _.eq('a', 'a');
		     * // => true
		     *
		     * _.eq('a', Object('a'));
		     * // => false
		     *
		     * _.eq(NaN, NaN);
		     * // => true
		     */
		    function eq(value, other) {
		      return value === other || (value !== value && other !== other);
		    }

		    /**
		     * Checks if `value` is greater than `other`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.9.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if `value` is greater than `other`,
		     *  else `false`.
		     * @see _.lt
		     * @example
		     *
		     * _.gt(3, 1);
		     * // => true
		     *
		     * _.gt(3, 3);
		     * // => false
		     *
		     * _.gt(1, 3);
		     * // => false
		     */
		    var gt = createRelationalOperation(baseGt);

		    /**
		     * Checks if `value` is greater than or equal to `other`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.9.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if `value` is greater than or equal to
		     *  `other`, else `false`.
		     * @see _.lte
		     * @example
		     *
		     * _.gte(3, 1);
		     * // => true
		     *
		     * _.gte(3, 3);
		     * // => true
		     *
		     * _.gte(1, 3);
		     * // => false
		     */
		    var gte = createRelationalOperation(function(value, other) {
		      return value >= other;
		    });

		    /**
		     * Checks if `value` is likely an `arguments` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
		     *  else `false`.
		     * @example
		     *
		     * _.isArguments(function() { return arguments; }());
		     * // => true
		     *
		     * _.isArguments([1, 2, 3]);
		     * // => false
		     */
		    var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
		      return isObjectLike(value) && hasOwnProperty.call(value, 'callee') &&
		        !propertyIsEnumerable.call(value, 'callee');
		    };

		    /**
		     * Checks if `value` is classified as an `Array` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an array, else `false`.
		     * @example
		     *
		     * _.isArray([1, 2, 3]);
		     * // => true
		     *
		     * _.isArray(document.body.children);
		     * // => false
		     *
		     * _.isArray('abc');
		     * // => false
		     *
		     * _.isArray(_.noop);
		     * // => false
		     */
		    var isArray = Array.isArray;

		    /**
		     * Checks if `value` is classified as an `ArrayBuffer` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.3.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
		     * @example
		     *
		     * _.isArrayBuffer(new ArrayBuffer(2));
		     * // => true
		     *
		     * _.isArrayBuffer(new Array(2));
		     * // => false
		     */
		    var isArrayBuffer = nodeIsArrayBuffer ? baseUnary(nodeIsArrayBuffer) : baseIsArrayBuffer;

		    /**
		     * Checks if `value` is array-like. A value is considered array-like if it's
		     * not a function and has a `value.length` that's an integer greater than or
		     * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
		     * @example
		     *
		     * _.isArrayLike([1, 2, 3]);
		     * // => true
		     *
		     * _.isArrayLike(document.body.children);
		     * // => true
		     *
		     * _.isArrayLike('abc');
		     * // => true
		     *
		     * _.isArrayLike(_.noop);
		     * // => false
		     */
		    function isArrayLike(value) {
		      return value != null && isLength(value.length) && !isFunction(value);
		    }

		    /**
		     * This method is like `_.isArrayLike` except that it also checks if `value`
		     * is an object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an array-like object,
		     *  else `false`.
		     * @example
		     *
		     * _.isArrayLikeObject([1, 2, 3]);
		     * // => true
		     *
		     * _.isArrayLikeObject(document.body.children);
		     * // => true
		     *
		     * _.isArrayLikeObject('abc');
		     * // => false
		     *
		     * _.isArrayLikeObject(_.noop);
		     * // => false
		     */
		    function isArrayLikeObject(value) {
		      return isObjectLike(value) && isArrayLike(value);
		    }

		    /**
		     * Checks if `value` is classified as a boolean primitive or object.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a boolean, else `false`.
		     * @example
		     *
		     * _.isBoolean(false);
		     * // => true
		     *
		     * _.isBoolean(null);
		     * // => false
		     */
		    function isBoolean(value) {
		      return value === true || value === false ||
		        (isObjectLike(value) && baseGetTag(value) == boolTag);
		    }

		    /**
		     * Checks if `value` is a buffer.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.3.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
		     * @example
		     *
		     * _.isBuffer(new Buffer(2));
		     * // => true
		     *
		     * _.isBuffer(new Uint8Array(2));
		     * // => false
		     */
		    var isBuffer = nativeIsBuffer || stubFalse;

		    /**
		     * Checks if `value` is classified as a `Date` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
		     * @example
		     *
		     * _.isDate(new Date);
		     * // => true
		     *
		     * _.isDate('Mon April 23 2012');
		     * // => false
		     */
		    var isDate = nodeIsDate ? baseUnary(nodeIsDate) : baseIsDate;

		    /**
		     * Checks if `value` is likely a DOM element.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a DOM element, else `false`.
		     * @example
		     *
		     * _.isElement(document.body);
		     * // => true
		     *
		     * _.isElement('<body>');
		     * // => false
		     */
		    function isElement(value) {
		      return isObjectLike(value) && value.nodeType === 1 && !isPlainObject(value);
		    }

		    /**
		     * Checks if `value` is an empty object, collection, map, or set.
		     *
		     * Objects are considered empty if they have no own enumerable string keyed
		     * properties.
		     *
		     * Array-like values such as `arguments` objects, arrays, buffers, strings, or
		     * jQuery-like collections are considered empty if they have a `length` of `0`.
		     * Similarly, maps and sets are considered empty if they have a `size` of `0`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is empty, else `false`.
		     * @example
		     *
		     * _.isEmpty(null);
		     * // => true
		     *
		     * _.isEmpty(true);
		     * // => true
		     *
		     * _.isEmpty(1);
		     * // => true
		     *
		     * _.isEmpty([1, 2, 3]);
		     * // => false
		     *
		     * _.isEmpty({ 'a': 1 });
		     * // => false
		     */
		    function isEmpty(value) {
		      if (value == null) {
		        return true;
		      }
		      if (isArrayLike(value) &&
		          (isArray(value) || typeof value == 'string' || typeof value.splice == 'function' ||
		            isBuffer(value) || isTypedArray(value) || isArguments(value))) {
		        return !value.length;
		      }
		      var tag = getTag(value);
		      if (tag == mapTag || tag == setTag) {
		        return !value.size;
		      }
		      if (isPrototype(value)) {
		        return !baseKeys(value).length;
		      }
		      for (var key in value) {
		        if (hasOwnProperty.call(value, key)) {
		          return false;
		        }
		      }
		      return true;
		    }

		    /**
		     * Performs a deep comparison between two values to determine if they are
		     * equivalent.
		     *
		     * **Note:** This method supports comparing arrays, array buffers, booleans,
		     * date objects, error objects, maps, numbers, `Object` objects, regexes,
		     * sets, strings, symbols, and typed arrays. `Object` objects are compared
		     * by their own, not inherited, enumerable properties. Functions and DOM
		     * nodes are compared by strict equality, i.e. `===`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
		     * @example
		     *
		     * var object = { 'a': 1 };
		     * var other = { 'a': 1 };
		     *
		     * _.isEqual(object, other);
		     * // => true
		     *
		     * object === other;
		     * // => false
		     */
		    function isEqual(value, other) {
		      return baseIsEqual(value, other);
		    }

		    /**
		     * This method is like `_.isEqual` except that it accepts `customizer` which
		     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
		     * are handled by the method instead. The `customizer` is invoked with up to
		     * six arguments: (objValue, othValue [, index|key, object, other, stack]).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @param {Function} [customizer] The function to customize comparisons.
		     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
		     * @example
		     *
		     * function isGreeting(value) {
		     *   return /^h(?:i|ello)$/.test(value);
		     * }
		     *
		     * function customizer(objValue, othValue) {
		     *   if (isGreeting(objValue) && isGreeting(othValue)) {
		     *     return true;
		     *   }
		     * }
		     *
		     * var array = ['hello', 'goodbye'];
		     * var other = ['hi', 'goodbye'];
		     *
		     * _.isEqualWith(array, other, customizer);
		     * // => true
		     */
		    function isEqualWith(value, other, customizer) {
		      customizer = typeof customizer == 'function' ? customizer : undefined$1;
		      var result = customizer ? customizer(value, other) : undefined$1;
		      return result === undefined$1 ? baseIsEqual(value, other, undefined$1, customizer) : !!result;
		    }

		    /**
		     * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
		     * `SyntaxError`, `TypeError`, or `URIError` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an error object, else `false`.
		     * @example
		     *
		     * _.isError(new Error);
		     * // => true
		     *
		     * _.isError(Error);
		     * // => false
		     */
		    function isError(value) {
		      if (!isObjectLike(value)) {
		        return false;
		      }
		      var tag = baseGetTag(value);
		      return tag == errorTag || tag == domExcTag ||
		        (typeof value.message == 'string' && typeof value.name == 'string' && !isPlainObject(value));
		    }

		    /**
		     * Checks if `value` is a finite primitive number.
		     *
		     * **Note:** This method is based on
		     * [`Number.isFinite`](https://mdn.io/Number/isFinite).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a finite number, else `false`.
		     * @example
		     *
		     * _.isFinite(3);
		     * // => true
		     *
		     * _.isFinite(Number.MIN_VALUE);
		     * // => true
		     *
		     * _.isFinite(Infinity);
		     * // => false
		     *
		     * _.isFinite('3');
		     * // => false
		     */
		    function isFinite(value) {
		      return typeof value == 'number' && nativeIsFinite(value);
		    }

		    /**
		     * Checks if `value` is classified as a `Function` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a function, else `false`.
		     * @example
		     *
		     * _.isFunction(_);
		     * // => true
		     *
		     * _.isFunction(/abc/);
		     * // => false
		     */
		    function isFunction(value) {
		      if (!isObject(value)) {
		        return false;
		      }
		      // The use of `Object#toString` avoids issues with the `typeof` operator
		      // in Safari 9 which returns 'object' for typed arrays and other constructors.
		      var tag = baseGetTag(value);
		      return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
		    }

		    /**
		     * Checks if `value` is an integer.
		     *
		     * **Note:** This method is based on
		     * [`Number.isInteger`](https://mdn.io/Number/isInteger).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
		     * @example
		     *
		     * _.isInteger(3);
		     * // => true
		     *
		     * _.isInteger(Number.MIN_VALUE);
		     * // => false
		     *
		     * _.isInteger(Infinity);
		     * // => false
		     *
		     * _.isInteger('3');
		     * // => false
		     */
		    function isInteger(value) {
		      return typeof value == 'number' && value == toInteger(value);
		    }

		    /**
		     * Checks if `value` is a valid array-like length.
		     *
		     * **Note:** This method is loosely based on
		     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
		     * @example
		     *
		     * _.isLength(3);
		     * // => true
		     *
		     * _.isLength(Number.MIN_VALUE);
		     * // => false
		     *
		     * _.isLength(Infinity);
		     * // => false
		     *
		     * _.isLength('3');
		     * // => false
		     */
		    function isLength(value) {
		      return typeof value == 'number' &&
		        value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
		    }

		    /**
		     * Checks if `value` is the
		     * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
		     * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is an object, else `false`.
		     * @example
		     *
		     * _.isObject({});
		     * // => true
		     *
		     * _.isObject([1, 2, 3]);
		     * // => true
		     *
		     * _.isObject(_.noop);
		     * // => true
		     *
		     * _.isObject(null);
		     * // => false
		     */
		    function isObject(value) {
		      var type = typeof value;
		      return value != null && (type == 'object' || type == 'function');
		    }

		    /**
		     * Checks if `value` is object-like. A value is object-like if it's not `null`
		     * and has a `typeof` result of "object".
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
		     * @example
		     *
		     * _.isObjectLike({});
		     * // => true
		     *
		     * _.isObjectLike([1, 2, 3]);
		     * // => true
		     *
		     * _.isObjectLike(_.noop);
		     * // => false
		     *
		     * _.isObjectLike(null);
		     * // => false
		     */
		    function isObjectLike(value) {
		      return value != null && typeof value == 'object';
		    }

		    /**
		     * Checks if `value` is classified as a `Map` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.3.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
		     * @example
		     *
		     * _.isMap(new Map);
		     * // => true
		     *
		     * _.isMap(new WeakMap);
		     * // => false
		     */
		    var isMap = nodeIsMap ? baseUnary(nodeIsMap) : baseIsMap;

		    /**
		     * Performs a partial deep comparison between `object` and `source` to
		     * determine if `object` contains equivalent property values.
		     *
		     * **Note:** This method is equivalent to `_.matches` when `source` is
		     * partially applied.
		     *
		     * Partial comparisons will match empty array and empty object `source`
		     * values against any array or object value, respectively. See `_.isEqual`
		     * for a list of supported value comparisons.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Lang
		     * @param {Object} object The object to inspect.
		     * @param {Object} source The object of property values to match.
		     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': 2 };
		     *
		     * _.isMatch(object, { 'b': 2 });
		     * // => true
		     *
		     * _.isMatch(object, { 'b': 1 });
		     * // => false
		     */
		    function isMatch(object, source) {
		      return object === source || baseIsMatch(object, source, getMatchData(source));
		    }

		    /**
		     * This method is like `_.isMatch` except that it accepts `customizer` which
		     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
		     * are handled by the method instead. The `customizer` is invoked with five
		     * arguments: (objValue, srcValue, index|key, object, source).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {Object} object The object to inspect.
		     * @param {Object} source The object of property values to match.
		     * @param {Function} [customizer] The function to customize comparisons.
		     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
		     * @example
		     *
		     * function isGreeting(value) {
		     *   return /^h(?:i|ello)$/.test(value);
		     * }
		     *
		     * function customizer(objValue, srcValue) {
		     *   if (isGreeting(objValue) && isGreeting(srcValue)) {
		     *     return true;
		     *   }
		     * }
		     *
		     * var object = { 'greeting': 'hello' };
		     * var source = { 'greeting': 'hi' };
		     *
		     * _.isMatchWith(object, source, customizer);
		     * // => true
		     */
		    function isMatchWith(object, source, customizer) {
		      customizer = typeof customizer == 'function' ? customizer : undefined$1;
		      return baseIsMatch(object, source, getMatchData(source), customizer);
		    }

		    /**
		     * Checks if `value` is `NaN`.
		     *
		     * **Note:** This method is based on
		     * [`Number.isNaN`](https://mdn.io/Number/isNaN) and is not the same as
		     * global [`isNaN`](https://mdn.io/isNaN) which returns `true` for
		     * `undefined` and other non-number values.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
		     * @example
		     *
		     * _.isNaN(NaN);
		     * // => true
		     *
		     * _.isNaN(new Number(NaN));
		     * // => true
		     *
		     * isNaN(undefined);
		     * // => true
		     *
		     * _.isNaN(undefined);
		     * // => false
		     */
		    function isNaN(value) {
		      // An `NaN` primitive is the only value that is not equal to itself.
		      // Perform the `toStringTag` check first to avoid errors with some
		      // ActiveX objects in IE.
		      return isNumber(value) && value != +value;
		    }

		    /**
		     * Checks if `value` is a pristine native function.
		     *
		     * **Note:** This method can't reliably detect native functions in the presence
		     * of the core-js package because core-js circumvents this kind of detection.
		     * Despite multiple requests, the core-js maintainer has made it clear: any
		     * attempt to fix the detection will be obstructed. As a result, we're left
		     * with little choice but to throw an error. Unfortunately, this also affects
		     * packages, like [babel-polyfill](https://www.npmjs.com/package/babel-polyfill),
		     * which rely on core-js.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a native function,
		     *  else `false`.
		     * @example
		     *
		     * _.isNative(Array.prototype.push);
		     * // => true
		     *
		     * _.isNative(_);
		     * // => false
		     */
		    function isNative(value) {
		      if (isMaskable(value)) {
		        throw new Error(CORE_ERROR_TEXT);
		      }
		      return baseIsNative(value);
		    }

		    /**
		     * Checks if `value` is `null`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is `null`, else `false`.
		     * @example
		     *
		     * _.isNull(null);
		     * // => true
		     *
		     * _.isNull(void 0);
		     * // => false
		     */
		    function isNull(value) {
		      return value === null;
		    }

		    /**
		     * Checks if `value` is `null` or `undefined`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
		     * @example
		     *
		     * _.isNil(null);
		     * // => true
		     *
		     * _.isNil(void 0);
		     * // => true
		     *
		     * _.isNil(NaN);
		     * // => false
		     */
		    function isNil(value) {
		      return value == null;
		    }

		    /**
		     * Checks if `value` is classified as a `Number` primitive or object.
		     *
		     * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
		     * classified as numbers, use the `_.isFinite` method.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a number, else `false`.
		     * @example
		     *
		     * _.isNumber(3);
		     * // => true
		     *
		     * _.isNumber(Number.MIN_VALUE);
		     * // => true
		     *
		     * _.isNumber(Infinity);
		     * // => true
		     *
		     * _.isNumber('3');
		     * // => false
		     */
		    function isNumber(value) {
		      return typeof value == 'number' ||
		        (isObjectLike(value) && baseGetTag(value) == numberTag);
		    }

		    /**
		     * Checks if `value` is a plain object, that is, an object created by the
		     * `Object` constructor or one with a `[[Prototype]]` of `null`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.8.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     * }
		     *
		     * _.isPlainObject(new Foo);
		     * // => false
		     *
		     * _.isPlainObject([1, 2, 3]);
		     * // => false
		     *
		     * _.isPlainObject({ 'x': 0, 'y': 0 });
		     * // => true
		     *
		     * _.isPlainObject(Object.create(null));
		     * // => true
		     */
		    function isPlainObject(value) {
		      if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
		        return false;
		      }
		      var proto = getPrototype(value);
		      if (proto === null) {
		        return true;
		      }
		      var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
		      return typeof Ctor == 'function' && Ctor instanceof Ctor &&
		        funcToString.call(Ctor) == objectCtorString;
		    }

		    /**
		     * Checks if `value` is classified as a `RegExp` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.1.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
		     * @example
		     *
		     * _.isRegExp(/abc/);
		     * // => true
		     *
		     * _.isRegExp('/abc/');
		     * // => false
		     */
		    var isRegExp = nodeIsRegExp ? baseUnary(nodeIsRegExp) : baseIsRegExp;

		    /**
		     * Checks if `value` is a safe integer. An integer is safe if it's an IEEE-754
		     * double precision number which isn't the result of a rounded unsafe integer.
		     *
		     * **Note:** This method is based on
		     * [`Number.isSafeInteger`](https://mdn.io/Number/isSafeInteger).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a safe integer, else `false`.
		     * @example
		     *
		     * _.isSafeInteger(3);
		     * // => true
		     *
		     * _.isSafeInteger(Number.MIN_VALUE);
		     * // => false
		     *
		     * _.isSafeInteger(Infinity);
		     * // => false
		     *
		     * _.isSafeInteger('3');
		     * // => false
		     */
		    function isSafeInteger(value) {
		      return isInteger(value) && value >= -MAX_SAFE_INTEGER && value <= MAX_SAFE_INTEGER;
		    }

		    /**
		     * Checks if `value` is classified as a `Set` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.3.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
		     * @example
		     *
		     * _.isSet(new Set);
		     * // => true
		     *
		     * _.isSet(new WeakSet);
		     * // => false
		     */
		    var isSet = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

		    /**
		     * Checks if `value` is classified as a `String` primitive or object.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a string, else `false`.
		     * @example
		     *
		     * _.isString('abc');
		     * // => true
		     *
		     * _.isString(1);
		     * // => false
		     */
		    function isString(value) {
		      return typeof value == 'string' ||
		        (!isArray(value) && isObjectLike(value) && baseGetTag(value) == stringTag);
		    }

		    /**
		     * Checks if `value` is classified as a `Symbol` primitive or object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
		     * @example
		     *
		     * _.isSymbol(Symbol.iterator);
		     * // => true
		     *
		     * _.isSymbol('abc');
		     * // => false
		     */
		    function isSymbol(value) {
		      return typeof value == 'symbol' ||
		        (isObjectLike(value) && baseGetTag(value) == symbolTag);
		    }

		    /**
		     * Checks if `value` is classified as a typed array.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
		     * @example
		     *
		     * _.isTypedArray(new Uint8Array);
		     * // => true
		     *
		     * _.isTypedArray([]);
		     * // => false
		     */
		    var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

		    /**
		     * Checks if `value` is `undefined`.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is `undefined`, else `false`.
		     * @example
		     *
		     * _.isUndefined(void 0);
		     * // => true
		     *
		     * _.isUndefined(null);
		     * // => false
		     */
		    function isUndefined(value) {
		      return value === undefined$1;
		    }

		    /**
		     * Checks if `value` is classified as a `WeakMap` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.3.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a weak map, else `false`.
		     * @example
		     *
		     * _.isWeakMap(new WeakMap);
		     * // => true
		     *
		     * _.isWeakMap(new Map);
		     * // => false
		     */
		    function isWeakMap(value) {
		      return isObjectLike(value) && getTag(value) == weakMapTag;
		    }

		    /**
		     * Checks if `value` is classified as a `WeakSet` object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.3.0
		     * @category Lang
		     * @param {*} value The value to check.
		     * @returns {boolean} Returns `true` if `value` is a weak set, else `false`.
		     * @example
		     *
		     * _.isWeakSet(new WeakSet);
		     * // => true
		     *
		     * _.isWeakSet(new Set);
		     * // => false
		     */
		    function isWeakSet(value) {
		      return isObjectLike(value) && baseGetTag(value) == weakSetTag;
		    }

		    /**
		     * Checks if `value` is less than `other`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.9.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if `value` is less than `other`,
		     *  else `false`.
		     * @see _.gt
		     * @example
		     *
		     * _.lt(1, 3);
		     * // => true
		     *
		     * _.lt(3, 3);
		     * // => false
		     *
		     * _.lt(3, 1);
		     * // => false
		     */
		    var lt = createRelationalOperation(baseLt);

		    /**
		     * Checks if `value` is less than or equal to `other`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.9.0
		     * @category Lang
		     * @param {*} value The value to compare.
		     * @param {*} other The other value to compare.
		     * @returns {boolean} Returns `true` if `value` is less than or equal to
		     *  `other`, else `false`.
		     * @see _.gte
		     * @example
		     *
		     * _.lte(1, 3);
		     * // => true
		     *
		     * _.lte(3, 3);
		     * // => true
		     *
		     * _.lte(3, 1);
		     * // => false
		     */
		    var lte = createRelationalOperation(function(value, other) {
		      return value <= other;
		    });

		    /**
		     * Converts `value` to an array.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {Array} Returns the converted array.
		     * @example
		     *
		     * _.toArray({ 'a': 1, 'b': 2 });
		     * // => [1, 2]
		     *
		     * _.toArray('abc');
		     * // => ['a', 'b', 'c']
		     *
		     * _.toArray(1);
		     * // => []
		     *
		     * _.toArray(null);
		     * // => []
		     */
		    function toArray(value) {
		      if (!value) {
		        return [];
		      }
		      if (isArrayLike(value)) {
		        return isString(value) ? stringToArray(value) : copyArray(value);
		      }
		      if (symIterator && value[symIterator]) {
		        return iteratorToArray(value[symIterator]());
		      }
		      var tag = getTag(value),
		          func = tag == mapTag ? mapToArray : (tag == setTag ? setToArray : values);

		      return func(value);
		    }

		    /**
		     * Converts `value` to a finite number.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.12.0
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {number} Returns the converted number.
		     * @example
		     *
		     * _.toFinite(3.2);
		     * // => 3.2
		     *
		     * _.toFinite(Number.MIN_VALUE);
		     * // => 5e-324
		     *
		     * _.toFinite(Infinity);
		     * // => 1.7976931348623157e+308
		     *
		     * _.toFinite('3.2');
		     * // => 3.2
		     */
		    function toFinite(value) {
		      if (!value) {
		        return value === 0 ? value : 0;
		      }
		      value = toNumber(value);
		      if (value === INFINITY || value === -INFINITY) {
		        var sign = (value < 0 ? -1 : 1);
		        return sign * MAX_INTEGER;
		      }
		      return value === value ? value : 0;
		    }

		    /**
		     * Converts `value` to an integer.
		     *
		     * **Note:** This method is loosely based on
		     * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {number} Returns the converted integer.
		     * @example
		     *
		     * _.toInteger(3.2);
		     * // => 3
		     *
		     * _.toInteger(Number.MIN_VALUE);
		     * // => 0
		     *
		     * _.toInteger(Infinity);
		     * // => 1.7976931348623157e+308
		     *
		     * _.toInteger('3.2');
		     * // => 3
		     */
		    function toInteger(value) {
		      var result = toFinite(value),
		          remainder = result % 1;

		      return result === result ? (remainder ? result - remainder : result) : 0;
		    }

		    /**
		     * Converts `value` to an integer suitable for use as the length of an
		     * array-like object.
		     *
		     * **Note:** This method is based on
		     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {number} Returns the converted integer.
		     * @example
		     *
		     * _.toLength(3.2);
		     * // => 3
		     *
		     * _.toLength(Number.MIN_VALUE);
		     * // => 0
		     *
		     * _.toLength(Infinity);
		     * // => 4294967295
		     *
		     * _.toLength('3.2');
		     * // => 3
		     */
		    function toLength(value) {
		      return value ? baseClamp(toInteger(value), 0, MAX_ARRAY_LENGTH) : 0;
		    }

		    /**
		     * Converts `value` to a number.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to process.
		     * @returns {number} Returns the number.
		     * @example
		     *
		     * _.toNumber(3.2);
		     * // => 3.2
		     *
		     * _.toNumber(Number.MIN_VALUE);
		     * // => 5e-324
		     *
		     * _.toNumber(Infinity);
		     * // => Infinity
		     *
		     * _.toNumber('3.2');
		     * // => 3.2
		     */
		    function toNumber(value) {
		      if (typeof value == 'number') {
		        return value;
		      }
		      if (isSymbol(value)) {
		        return NAN;
		      }
		      if (isObject(value)) {
		        var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
		        value = isObject(other) ? (other + '') : other;
		      }
		      if (typeof value != 'string') {
		        return value === 0 ? value : +value;
		      }
		      value = baseTrim(value);
		      var isBinary = reIsBinary.test(value);
		      return (isBinary || reIsOctal.test(value))
		        ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
		        : (reIsBadHex.test(value) ? NAN : +value);
		    }

		    /**
		     * Converts `value` to a plain object flattening inherited enumerable string
		     * keyed properties of `value` to own properties of the plain object.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {Object} Returns the converted plain object.
		     * @example
		     *
		     * function Foo() {
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.assign({ 'a': 1 }, new Foo);
		     * // => { 'a': 1, 'b': 2 }
		     *
		     * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
		     * // => { 'a': 1, 'b': 2, 'c': 3 }
		     */
		    function toPlainObject(value) {
		      return copyObject(value, keysIn(value));
		    }

		    /**
		     * Converts `value` to a safe integer. A safe integer can be compared and
		     * represented correctly.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {number} Returns the converted integer.
		     * @example
		     *
		     * _.toSafeInteger(3.2);
		     * // => 3
		     *
		     * _.toSafeInteger(Number.MIN_VALUE);
		     * // => 0
		     *
		     * _.toSafeInteger(Infinity);
		     * // => 9007199254740991
		     *
		     * _.toSafeInteger('3.2');
		     * // => 3
		     */
		    function toSafeInteger(value) {
		      return value
		        ? baseClamp(toInteger(value), -MAX_SAFE_INTEGER, MAX_SAFE_INTEGER)
		        : (value === 0 ? value : 0);
		    }

		    /**
		     * Converts `value` to a string. An empty string is returned for `null`
		     * and `undefined` values. The sign of `-0` is preserved.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Lang
		     * @param {*} value The value to convert.
		     * @returns {string} Returns the converted string.
		     * @example
		     *
		     * _.toString(null);
		     * // => ''
		     *
		     * _.toString(-0);
		     * // => '-0'
		     *
		     * _.toString([1, 2, 3]);
		     * // => '1,2,3'
		     */
		    function toString(value) {
		      return value == null ? '' : baseToString(value);
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Assigns own enumerable string keyed properties of source objects to the
		     * destination object. Source objects are applied from left to right.
		     * Subsequent sources overwrite property assignments of previous sources.
		     *
		     * **Note:** This method mutates `object` and is loosely based on
		     * [`Object.assign`](https://mdn.io/Object/assign).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.10.0
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} [sources] The source objects.
		     * @returns {Object} Returns `object`.
		     * @see _.assignIn
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     * }
		     *
		     * function Bar() {
		     *   this.c = 3;
		     * }
		     *
		     * Foo.prototype.b = 2;
		     * Bar.prototype.d = 4;
		     *
		     * _.assign({ 'a': 0 }, new Foo, new Bar);
		     * // => { 'a': 1, 'c': 3 }
		     */
		    var assign = createAssigner(function(object, source) {
		      if (isPrototype(source) || isArrayLike(source)) {
		        copyObject(source, keys(source), object);
		        return;
		      }
		      for (var key in source) {
		        if (hasOwnProperty.call(source, key)) {
		          assignValue(object, key, source[key]);
		        }
		      }
		    });

		    /**
		     * This method is like `_.assign` except that it iterates over own and
		     * inherited source properties.
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @alias extend
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} [sources] The source objects.
		     * @returns {Object} Returns `object`.
		     * @see _.assign
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     * }
		     *
		     * function Bar() {
		     *   this.c = 3;
		     * }
		     *
		     * Foo.prototype.b = 2;
		     * Bar.prototype.d = 4;
		     *
		     * _.assignIn({ 'a': 0 }, new Foo, new Bar);
		     * // => { 'a': 1, 'b': 2, 'c': 3, 'd': 4 }
		     */
		    var assignIn = createAssigner(function(object, source) {
		      copyObject(source, keysIn(source), object);
		    });

		    /**
		     * This method is like `_.assignIn` except that it accepts `customizer`
		     * which is invoked to produce the assigned values. If `customizer` returns
		     * `undefined`, assignment is handled by the method instead. The `customizer`
		     * is invoked with five arguments: (objValue, srcValue, key, object, source).
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @alias extendWith
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} sources The source objects.
		     * @param {Function} [customizer] The function to customize assigned values.
		     * @returns {Object} Returns `object`.
		     * @see _.assignWith
		     * @example
		     *
		     * function customizer(objValue, srcValue) {
		     *   return _.isUndefined(objValue) ? srcValue : objValue;
		     * }
		     *
		     * var defaults = _.partialRight(_.assignInWith, customizer);
		     *
		     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
		     * // => { 'a': 1, 'b': 2 }
		     */
		    var assignInWith = createAssigner(function(object, source, srcIndex, customizer) {
		      copyObject(source, keysIn(source), object, customizer);
		    });

		    /**
		     * This method is like `_.assign` except that it accepts `customizer`
		     * which is invoked to produce the assigned values. If `customizer` returns
		     * `undefined`, assignment is handled by the method instead. The `customizer`
		     * is invoked with five arguments: (objValue, srcValue, key, object, source).
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} sources The source objects.
		     * @param {Function} [customizer] The function to customize assigned values.
		     * @returns {Object} Returns `object`.
		     * @see _.assignInWith
		     * @example
		     *
		     * function customizer(objValue, srcValue) {
		     *   return _.isUndefined(objValue) ? srcValue : objValue;
		     * }
		     *
		     * var defaults = _.partialRight(_.assignWith, customizer);
		     *
		     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
		     * // => { 'a': 1, 'b': 2 }
		     */
		    var assignWith = createAssigner(function(object, source, srcIndex, customizer) {
		      copyObject(source, keys(source), object, customizer);
		    });

		    /**
		     * Creates an array of values corresponding to `paths` of `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.0.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {...(string|string[])} [paths] The property paths to pick.
		     * @returns {Array} Returns the picked values.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
		     *
		     * _.at(object, ['a[0].b.c', 'a[1]']);
		     * // => [3, 4]
		     */
		    var at = flatRest(baseAt);

		    /**
		     * Creates an object that inherits from the `prototype` object. If a
		     * `properties` object is given, its own enumerable string keyed properties
		     * are assigned to the created object.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.3.0
		     * @category Object
		     * @param {Object} prototype The object to inherit from.
		     * @param {Object} [properties] The properties to assign to the object.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * function Shape() {
		     *   this.x = 0;
		     *   this.y = 0;
		     * }
		     *
		     * function Circle() {
		     *   Shape.call(this);
		     * }
		     *
		     * Circle.prototype = _.create(Shape.prototype, {
		     *   'constructor': Circle
		     * });
		     *
		     * var circle = new Circle;
		     * circle instanceof Circle;
		     * // => true
		     *
		     * circle instanceof Shape;
		     * // => true
		     */
		    function create(prototype, properties) {
		      var result = baseCreate(prototype);
		      return properties == null ? result : baseAssign(result, properties);
		    }

		    /**
		     * Assigns own and inherited enumerable string keyed properties of source
		     * objects to the destination object for all destination properties that
		     * resolve to `undefined`. Source objects are applied from left to right.
		     * Once a property is set, additional values of the same property are ignored.
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} [sources] The source objects.
		     * @returns {Object} Returns `object`.
		     * @see _.defaultsDeep
		     * @example
		     *
		     * _.defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
		     * // => { 'a': 1, 'b': 2 }
		     */
		    var defaults = baseRest(function(object, sources) {
		      object = Object(object);

		      var index = -1;
		      var length = sources.length;
		      var guard = length > 2 ? sources[2] : undefined$1;

		      if (guard && isIterateeCall(sources[0], sources[1], guard)) {
		        length = 1;
		      }

		      while (++index < length) {
		        var source = sources[index];
		        var props = keysIn(source);
		        var propsIndex = -1;
		        var propsLength = props.length;

		        while (++propsIndex < propsLength) {
		          var key = props[propsIndex];
		          var value = object[key];

		          if (value === undefined$1 ||
		              (eq(value, objectProto[key]) && !hasOwnProperty.call(object, key))) {
		            object[key] = source[key];
		          }
		        }
		      }

		      return object;
		    });

		    /**
		     * This method is like `_.defaults` except that it recursively assigns
		     * default properties.
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.10.0
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} [sources] The source objects.
		     * @returns {Object} Returns `object`.
		     * @see _.defaults
		     * @example
		     *
		     * _.defaultsDeep({ 'a': { 'b': 2 } }, { 'a': { 'b': 1, 'c': 3 } });
		     * // => { 'a': { 'b': 2, 'c': 3 } }
		     */
		    var defaultsDeep = baseRest(function(args) {
		      args.push(undefined$1, customDefaultsMerge);
		      return apply(mergeWith, undefined$1, args);
		    });

		    /**
		     * This method is like `_.find` except that it returns the key of the first
		     * element `predicate` returns truthy for instead of the element itself.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.1.0
		     * @category Object
		     * @param {Object} object The object to inspect.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {string|undefined} Returns the key of the matched element,
		     *  else `undefined`.
		     * @example
		     *
		     * var users = {
		     *   'barney':  { 'age': 36, 'active': true },
		     *   'fred':    { 'age': 40, 'active': false },
		     *   'pebbles': { 'age': 1,  'active': true }
		     * };
		     *
		     * _.findKey(users, function(o) { return o.age < 40; });
		     * // => 'barney' (iteration order is not guaranteed)
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.findKey(users, { 'age': 1, 'active': true });
		     * // => 'pebbles'
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.findKey(users, ['active', false]);
		     * // => 'fred'
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.findKey(users, 'active');
		     * // => 'barney'
		     */
		    function findKey(object, predicate) {
		      return baseFindKey(object, getIteratee(predicate, 3), baseForOwn);
		    }

		    /**
		     * This method is like `_.findKey` except that it iterates over elements of
		     * a collection in the opposite order.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Object
		     * @param {Object} object The object to inspect.
		     * @param {Function} [predicate=_.identity] The function invoked per iteration.
		     * @returns {string|undefined} Returns the key of the matched element,
		     *  else `undefined`.
		     * @example
		     *
		     * var users = {
		     *   'barney':  { 'age': 36, 'active': true },
		     *   'fred':    { 'age': 40, 'active': false },
		     *   'pebbles': { 'age': 1,  'active': true }
		     * };
		     *
		     * _.findLastKey(users, function(o) { return o.age < 40; });
		     * // => returns 'pebbles' assuming `_.findKey` returns 'barney'
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.findLastKey(users, { 'age': 36, 'active': true });
		     * // => 'barney'
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.findLastKey(users, ['active', false]);
		     * // => 'fred'
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.findLastKey(users, 'active');
		     * // => 'pebbles'
		     */
		    function findLastKey(object, predicate) {
		      return baseFindKey(object, getIteratee(predicate, 3), baseForOwnRight);
		    }

		    /**
		     * Iterates over own and inherited enumerable string keyed properties of an
		     * object and invokes `iteratee` for each property. The iteratee is invoked
		     * with three arguments: (value, key, object). Iteratee functions may exit
		     * iteration early by explicitly returning `false`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.3.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Object} Returns `object`.
		     * @see _.forInRight
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.forIn(new Foo, function(value, key) {
		     *   console.log(key);
		     * });
		     * // => Logs 'a', 'b', then 'c' (iteration order is not guaranteed).
		     */
		    function forIn(object, iteratee) {
		      return object == null
		        ? object
		        : baseFor(object, getIteratee(iteratee, 3), keysIn);
		    }

		    /**
		     * This method is like `_.forIn` except that it iterates over properties of
		     * `object` in the opposite order.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Object} Returns `object`.
		     * @see _.forIn
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.forInRight(new Foo, function(value, key) {
		     *   console.log(key);
		     * });
		     * // => Logs 'c', 'b', then 'a' assuming `_.forIn` logs 'a', 'b', then 'c'.
		     */
		    function forInRight(object, iteratee) {
		      return object == null
		        ? object
		        : baseForRight(object, getIteratee(iteratee, 3), keysIn);
		    }

		    /**
		     * Iterates over own enumerable string keyed properties of an object and
		     * invokes `iteratee` for each property. The iteratee is invoked with three
		     * arguments: (value, key, object). Iteratee functions may exit iteration
		     * early by explicitly returning `false`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.3.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Object} Returns `object`.
		     * @see _.forOwnRight
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.forOwn(new Foo, function(value, key) {
		     *   console.log(key);
		     * });
		     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
		     */
		    function forOwn(object, iteratee) {
		      return object && baseForOwn(object, getIteratee(iteratee, 3));
		    }

		    /**
		     * This method is like `_.forOwn` except that it iterates over properties of
		     * `object` in the opposite order.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.0.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Object} Returns `object`.
		     * @see _.forOwn
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.forOwnRight(new Foo, function(value, key) {
		     *   console.log(key);
		     * });
		     * // => Logs 'b' then 'a' assuming `_.forOwn` logs 'a' then 'b'.
		     */
		    function forOwnRight(object, iteratee) {
		      return object && baseForOwnRight(object, getIteratee(iteratee, 3));
		    }

		    /**
		     * Creates an array of function property names from own enumerable properties
		     * of `object`.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The object to inspect.
		     * @returns {Array} Returns the function names.
		     * @see _.functionsIn
		     * @example
		     *
		     * function Foo() {
		     *   this.a = _.constant('a');
		     *   this.b = _.constant('b');
		     * }
		     *
		     * Foo.prototype.c = _.constant('c');
		     *
		     * _.functions(new Foo);
		     * // => ['a', 'b']
		     */
		    function functions(object) {
		      return object == null ? [] : baseFunctions(object, keys(object));
		    }

		    /**
		     * Creates an array of function property names from own and inherited
		     * enumerable properties of `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The object to inspect.
		     * @returns {Array} Returns the function names.
		     * @see _.functions
		     * @example
		     *
		     * function Foo() {
		     *   this.a = _.constant('a');
		     *   this.b = _.constant('b');
		     * }
		     *
		     * Foo.prototype.c = _.constant('c');
		     *
		     * _.functionsIn(new Foo);
		     * // => ['a', 'b', 'c']
		     */
		    function functionsIn(object) {
		      return object == null ? [] : baseFunctions(object, keysIn(object));
		    }

		    /**
		     * Gets the value at `path` of `object`. If the resolved value is
		     * `undefined`, the `defaultValue` is returned in its place.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.7.0
		     * @category Object
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path of the property to get.
		     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
		     * @returns {*} Returns the resolved value.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
		     *
		     * _.get(object, 'a[0].b.c');
		     * // => 3
		     *
		     * _.get(object, ['a', '0', 'b', 'c']);
		     * // => 3
		     *
		     * _.get(object, 'a.b.c', 'default');
		     * // => 'default'
		     */
		    function get(object, path, defaultValue) {
		      var result = object == null ? undefined$1 : baseGet(object, path);
		      return result === undefined$1 ? defaultValue : result;
		    }

		    /**
		     * Checks if `path` is a direct property of `object`.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path to check.
		     * @returns {boolean} Returns `true` if `path` exists, else `false`.
		     * @example
		     *
		     * var object = { 'a': { 'b': 2 } };
		     * var other = _.create({ 'a': _.create({ 'b': 2 }) });
		     *
		     * _.has(object, 'a');
		     * // => true
		     *
		     * _.has(object, 'a.b');
		     * // => true
		     *
		     * _.has(object, ['a', 'b']);
		     * // => true
		     *
		     * _.has(other, 'a');
		     * // => false
		     */
		    function has(object, path) {
		      return object != null && hasPath(object, path, baseHas);
		    }

		    /**
		     * Checks if `path` is a direct or inherited property of `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path to check.
		     * @returns {boolean} Returns `true` if `path` exists, else `false`.
		     * @example
		     *
		     * var object = _.create({ 'a': _.create({ 'b': 2 }) });
		     *
		     * _.hasIn(object, 'a');
		     * // => true
		     *
		     * _.hasIn(object, 'a.b');
		     * // => true
		     *
		     * _.hasIn(object, ['a', 'b']);
		     * // => true
		     *
		     * _.hasIn(object, 'b');
		     * // => false
		     */
		    function hasIn(object, path) {
		      return object != null && hasPath(object, path, baseHasIn);
		    }

		    /**
		     * Creates an object composed of the inverted keys and values of `object`.
		     * If `object` contains duplicate values, subsequent values overwrite
		     * property assignments of previous values.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.7.0
		     * @category Object
		     * @param {Object} object The object to invert.
		     * @returns {Object} Returns the new inverted object.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': 2, 'c': 1 };
		     *
		     * _.invert(object);
		     * // => { '1': 'c', '2': 'b' }
		     */
		    var invert = createInverter(function(result, value, key) {
		      if (value != null &&
		          typeof value.toString != 'function') {
		        value = nativeObjectToString.call(value);
		      }

		      result[value] = key;
		    }, constant(identity));

		    /**
		     * This method is like `_.invert` except that the inverted object is generated
		     * from the results of running each element of `object` thru `iteratee`. The
		     * corresponding inverted value of each inverted key is an array of keys
		     * responsible for generating the inverted value. The iteratee is invoked
		     * with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.1.0
		     * @category Object
		     * @param {Object} object The object to invert.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {Object} Returns the new inverted object.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': 2, 'c': 1 };
		     *
		     * _.invertBy(object);
		     * // => { '1': ['a', 'c'], '2': ['b'] }
		     *
		     * _.invertBy(object, function(value) {
		     *   return 'group' + value;
		     * });
		     * // => { 'group1': ['a', 'c'], 'group2': ['b'] }
		     */
		    var invertBy = createInverter(function(result, value, key) {
		      if (value != null &&
		          typeof value.toString != 'function') {
		        value = nativeObjectToString.call(value);
		      }

		      if (hasOwnProperty.call(result, value)) {
		        result[value].push(key);
		      } else {
		        result[value] = [key];
		      }
		    }, getIteratee);

		    /**
		     * Invokes the method at `path` of `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path of the method to invoke.
		     * @param {...*} [args] The arguments to invoke the method with.
		     * @returns {*} Returns the result of the invoked method.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': [1, 2, 3, 4] } }] };
		     *
		     * _.invoke(object, 'a[0].b.c.slice', 1, 3);
		     * // => [2, 3]
		     */
		    var invoke = baseRest(baseInvoke);

		    /**
		     * Creates an array of the own enumerable property names of `object`.
		     *
		     * **Note:** Non-object values are coerced to objects. See the
		     * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
		     * for more details.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.keys(new Foo);
		     * // => ['a', 'b'] (iteration order is not guaranteed)
		     *
		     * _.keys('hi');
		     * // => ['0', '1']
		     */
		    function keys(object) {
		      return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
		    }

		    /**
		     * Creates an array of the own and inherited enumerable property names of `object`.
		     *
		     * **Note:** Non-object values are coerced to objects.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Object
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property names.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.keysIn(new Foo);
		     * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
		     */
		    function keysIn(object) {
		      return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
		    }

		    /**
		     * The opposite of `_.mapValues`; this method creates an object with the
		     * same values as `object` and keys generated by running each own enumerable
		     * string keyed property of `object` thru `iteratee`. The iteratee is invoked
		     * with three arguments: (value, key, object).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.8.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Object} Returns the new mapped object.
		     * @see _.mapValues
		     * @example
		     *
		     * _.mapKeys({ 'a': 1, 'b': 2 }, function(value, key) {
		     *   return key + value;
		     * });
		     * // => { 'a1': 1, 'b2': 2 }
		     */
		    function mapKeys(object, iteratee) {
		      var result = {};
		      iteratee = getIteratee(iteratee, 3);

		      baseForOwn(object, function(value, key, object) {
		        baseAssignValue(result, iteratee(value, key, object), value);
		      });
		      return result;
		    }

		    /**
		     * Creates an object with the same keys as `object` and values generated
		     * by running each own enumerable string keyed property of `object` thru
		     * `iteratee`. The iteratee is invoked with three arguments:
		     * (value, key, object).
		     *
		     * @static
		     * @memberOf _
		     * @since 2.4.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Object} Returns the new mapped object.
		     * @see _.mapKeys
		     * @example
		     *
		     * var users = {
		     *   'fred':    { 'user': 'fred',    'age': 40 },
		     *   'pebbles': { 'user': 'pebbles', 'age': 1 }
		     * };
		     *
		     * _.mapValues(users, function(o) { return o.age; });
		     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.mapValues(users, 'age');
		     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
		     */
		    function mapValues(object, iteratee) {
		      var result = {};
		      iteratee = getIteratee(iteratee, 3);

		      baseForOwn(object, function(value, key, object) {
		        baseAssignValue(result, key, iteratee(value, key, object));
		      });
		      return result;
		    }

		    /**
		     * This method is like `_.assign` except that it recursively merges own and
		     * inherited enumerable string keyed properties of source objects into the
		     * destination object. Source properties that resolve to `undefined` are
		     * skipped if a destination value exists. Array and plain object properties
		     * are merged recursively. Other objects and value types are overridden by
		     * assignment. Source objects are applied from left to right. Subsequent
		     * sources overwrite property assignments of previous sources.
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.5.0
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} [sources] The source objects.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * var object = {
		     *   'a': [{ 'b': 2 }, { 'd': 4 }]
		     * };
		     *
		     * var other = {
		     *   'a': [{ 'c': 3 }, { 'e': 5 }]
		     * };
		     *
		     * _.merge(object, other);
		     * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
		     */
		    var merge = createAssigner(function(object, source, srcIndex) {
		      baseMerge(object, source, srcIndex);
		    });

		    /**
		     * This method is like `_.merge` except that it accepts `customizer` which
		     * is invoked to produce the merged values of the destination and source
		     * properties. If `customizer` returns `undefined`, merging is handled by the
		     * method instead. The `customizer` is invoked with six arguments:
		     * (objValue, srcValue, key, object, source, stack).
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The destination object.
		     * @param {...Object} sources The source objects.
		     * @param {Function} customizer The function to customize assigned values.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * function customizer(objValue, srcValue) {
		     *   if (_.isArray(objValue)) {
		     *     return objValue.concat(srcValue);
		     *   }
		     * }
		     *
		     * var object = { 'a': [1], 'b': [2] };
		     * var other = { 'a': [3], 'b': [4] };
		     *
		     * _.mergeWith(object, other, customizer);
		     * // => { 'a': [1, 3], 'b': [2, 4] }
		     */
		    var mergeWith = createAssigner(function(object, source, srcIndex, customizer) {
		      baseMerge(object, source, srcIndex, customizer);
		    });

		    /**
		     * The opposite of `_.pick`; this method creates an object composed of the
		     * own and inherited enumerable property paths of `object` that are not omitted.
		     *
		     * **Note:** This method is considerably slower than `_.pick`.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The source object.
		     * @param {...(string|string[])} [paths] The property paths to omit.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': '2', 'c': 3 };
		     *
		     * _.omit(object, ['a', 'c']);
		     * // => { 'b': '2' }
		     */
		    var omit = flatRest(function(object, paths) {
		      var result = {};
		      if (object == null) {
		        return result;
		      }
		      var isDeep = false;
		      paths = arrayMap(paths, function(path) {
		        path = castPath(path, object);
		        isDeep || (isDeep = path.length > 1);
		        return path;
		      });
		      copyObject(object, getAllKeysIn(object), result);
		      if (isDeep) {
		        result = baseClone(result, CLONE_DEEP_FLAG | CLONE_FLAT_FLAG | CLONE_SYMBOLS_FLAG, customOmitClone);
		      }
		      var length = paths.length;
		      while (length--) {
		        baseUnset(result, paths[length]);
		      }
		      return result;
		    });

		    /**
		     * The opposite of `_.pickBy`; this method creates an object composed of
		     * the own and inherited enumerable string keyed properties of `object` that
		     * `predicate` doesn't return truthy for. The predicate is invoked with two
		     * arguments: (value, key).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The source object.
		     * @param {Function} [predicate=_.identity] The function invoked per property.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': '2', 'c': 3 };
		     *
		     * _.omitBy(object, _.isNumber);
		     * // => { 'b': '2' }
		     */
		    function omitBy(object, predicate) {
		      return pickBy(object, negate(getIteratee(predicate)));
		    }

		    /**
		     * Creates an object composed of the picked `object` properties.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The source object.
		     * @param {...(string|string[])} [paths] The property paths to pick.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': '2', 'c': 3 };
		     *
		     * _.pick(object, ['a', 'c']);
		     * // => { 'a': 1, 'c': 3 }
		     */
		    var pick = flatRest(function(object, paths) {
		      return object == null ? {} : basePick(object, paths);
		    });

		    /**
		     * Creates an object composed of the `object` properties `predicate` returns
		     * truthy for. The predicate is invoked with two arguments: (value, key).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The source object.
		     * @param {Function} [predicate=_.identity] The function invoked per property.
		     * @returns {Object} Returns the new object.
		     * @example
		     *
		     * var object = { 'a': 1, 'b': '2', 'c': 3 };
		     *
		     * _.pickBy(object, _.isNumber);
		     * // => { 'a': 1, 'c': 3 }
		     */
		    function pickBy(object, predicate) {
		      if (object == null) {
		        return {};
		      }
		      var props = arrayMap(getAllKeysIn(object), function(prop) {
		        return [prop];
		      });
		      predicate = getIteratee(predicate);
		      return basePickBy(object, props, function(value, path) {
		        return predicate(value, path[0]);
		      });
		    }

		    /**
		     * This method is like `_.get` except that if the resolved value is a
		     * function it's invoked with the `this` binding of its parent object and
		     * its result is returned.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The object to query.
		     * @param {Array|string} path The path of the property to resolve.
		     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
		     * @returns {*} Returns the resolved value.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c1': 3, 'c2': _.constant(4) } }] };
		     *
		     * _.result(object, 'a[0].b.c1');
		     * // => 3
		     *
		     * _.result(object, 'a[0].b.c2');
		     * // => 4
		     *
		     * _.result(object, 'a[0].b.c3', 'default');
		     * // => 'default'
		     *
		     * _.result(object, 'a[0].b.c3', _.constant('default'));
		     * // => 'default'
		     */
		    function result(object, path, defaultValue) {
		      path = castPath(path, object);

		      var index = -1,
		          length = path.length;

		      // Ensure the loop is entered when path is empty.
		      if (!length) {
		        length = 1;
		        object = undefined$1;
		      }
		      while (++index < length) {
		        var value = object == null ? undefined$1 : object[toKey(path[index])];
		        if (value === undefined$1) {
		          index = length;
		          value = defaultValue;
		        }
		        object = isFunction(value) ? value.call(object) : value;
		      }
		      return object;
		    }

		    /**
		     * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
		     * it's created. Arrays are created for missing index properties while objects
		     * are created for all other missing properties. Use `_.setWith` to customize
		     * `path` creation.
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.7.0
		     * @category Object
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to set.
		     * @param {*} value The value to set.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
		     *
		     * _.set(object, 'a[0].b.c', 4);
		     * console.log(object.a[0].b.c);
		     * // => 4
		     *
		     * _.set(object, ['x', '0', 'y', 'z'], 5);
		     * console.log(object.x[0].y.z);
		     * // => 5
		     */
		    function set(object, path, value) {
		      return object == null ? object : baseSet(object, path, value);
		    }

		    /**
		     * This method is like `_.set` except that it accepts `customizer` which is
		     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
		     * path creation is handled by the method instead. The `customizer` is invoked
		     * with three arguments: (nsValue, key, nsObject).
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to set.
		     * @param {*} value The value to set.
		     * @param {Function} [customizer] The function to customize assigned values.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * var object = {};
		     *
		     * _.setWith(object, '[0][1]', 'a', Object);
		     * // => { '0': { '1': 'a' } }
		     */
		    function setWith(object, path, value, customizer) {
		      customizer = typeof customizer == 'function' ? customizer : undefined$1;
		      return object == null ? object : baseSet(object, path, value, customizer);
		    }

		    /**
		     * Creates an array of own enumerable string keyed-value pairs for `object`
		     * which can be consumed by `_.fromPairs`. If `object` is a map or set, its
		     * entries are returned.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @alias entries
		     * @category Object
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the key-value pairs.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.toPairs(new Foo);
		     * // => [['a', 1], ['b', 2]] (iteration order is not guaranteed)
		     */
		    var toPairs = createToPairs(keys);

		    /**
		     * Creates an array of own and inherited enumerable string keyed-value pairs
		     * for `object` which can be consumed by `_.fromPairs`. If `object` is a map
		     * or set, its entries are returned.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @alias entriesIn
		     * @category Object
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the key-value pairs.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.toPairsIn(new Foo);
		     * // => [['a', 1], ['b', 2], ['c', 3]] (iteration order is not guaranteed)
		     */
		    var toPairsIn = createToPairs(keysIn);

		    /**
		     * An alternative to `_.reduce`; this method transforms `object` to a new
		     * `accumulator` object which is the result of running each of its own
		     * enumerable string keyed properties thru `iteratee`, with each invocation
		     * potentially mutating the `accumulator` object. If `accumulator` is not
		     * provided, a new object with the same `[[Prototype]]` will be used. The
		     * iteratee is invoked with four arguments: (accumulator, value, key, object).
		     * Iteratee functions may exit iteration early by explicitly returning `false`.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.3.0
		     * @category Object
		     * @param {Object} object The object to iterate over.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @param {*} [accumulator] The custom accumulator value.
		     * @returns {*} Returns the accumulated value.
		     * @example
		     *
		     * _.transform([2, 3, 4], function(result, n) {
		     *   result.push(n *= n);
		     *   return n % 2 == 0;
		     * }, []);
		     * // => [4, 9]
		     *
		     * _.transform({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
		     *   (result[value] || (result[value] = [])).push(key);
		     * }, {});
		     * // => { '1': ['a', 'c'], '2': ['b'] }
		     */
		    function transform(object, iteratee, accumulator) {
		      var isArr = isArray(object),
		          isArrLike = isArr || isBuffer(object) || isTypedArray(object);

		      iteratee = getIteratee(iteratee, 4);
		      if (accumulator == null) {
		        var Ctor = object && object.constructor;
		        if (isArrLike) {
		          accumulator = isArr ? new Ctor : [];
		        }
		        else if (isObject(object)) {
		          accumulator = isFunction(Ctor) ? baseCreate(getPrototype(object)) : {};
		        }
		        else {
		          accumulator = {};
		        }
		      }
		      (isArrLike ? arrayEach : baseForOwn)(object, function(value, index, object) {
		        return iteratee(accumulator, value, index, object);
		      });
		      return accumulator;
		    }

		    /**
		     * Removes the property at `path` of `object`.
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Object
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to unset.
		     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': 7 } }] };
		     * _.unset(object, 'a[0].b.c');
		     * // => true
		     *
		     * console.log(object);
		     * // => { 'a': [{ 'b': {} }] };
		     *
		     * _.unset(object, ['a', '0', 'b', 'c']);
		     * // => true
		     *
		     * console.log(object);
		     * // => { 'a': [{ 'b': {} }] };
		     */
		    function unset(object, path) {
		      return object == null ? true : baseUnset(object, path);
		    }

		    /**
		     * This method is like `_.set` except that accepts `updater` to produce the
		     * value to set. Use `_.updateWith` to customize `path` creation. The `updater`
		     * is invoked with one argument: (value).
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.6.0
		     * @category Object
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to set.
		     * @param {Function} updater The function to produce the updated value.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
		     *
		     * _.update(object, 'a[0].b.c', function(n) { return n * n; });
		     * console.log(object.a[0].b.c);
		     * // => 9
		     *
		     * _.update(object, 'x[0].y.z', function(n) { return n ? n + 1 : 0; });
		     * console.log(object.x[0].y.z);
		     * // => 0
		     */
		    function update(object, path, updater) {
		      return object == null ? object : baseUpdate(object, path, castFunction(updater));
		    }

		    /**
		     * This method is like `_.update` except that it accepts `customizer` which is
		     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
		     * path creation is handled by the method instead. The `customizer` is invoked
		     * with three arguments: (nsValue, key, nsObject).
		     *
		     * **Note:** This method mutates `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.6.0
		     * @category Object
		     * @param {Object} object The object to modify.
		     * @param {Array|string} path The path of the property to set.
		     * @param {Function} updater The function to produce the updated value.
		     * @param {Function} [customizer] The function to customize assigned values.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * var object = {};
		     *
		     * _.updateWith(object, '[0][1]', _.constant('a'), Object);
		     * // => { '0': { '1': 'a' } }
		     */
		    function updateWith(object, path, updater, customizer) {
		      customizer = typeof customizer == 'function' ? customizer : undefined$1;
		      return object == null ? object : baseUpdate(object, path, castFunction(updater), customizer);
		    }

		    /**
		     * Creates an array of the own enumerable string keyed property values of `object`.
		     *
		     * **Note:** Non-object values are coerced to objects.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Object
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property values.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.values(new Foo);
		     * // => [1, 2] (iteration order is not guaranteed)
		     *
		     * _.values('hi');
		     * // => ['h', 'i']
		     */
		    function values(object) {
		      return object == null ? [] : baseValues(object, keys(object));
		    }

		    /**
		     * Creates an array of the own and inherited enumerable string keyed property
		     * values of `object`.
		     *
		     * **Note:** Non-object values are coerced to objects.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Object
		     * @param {Object} object The object to query.
		     * @returns {Array} Returns the array of property values.
		     * @example
		     *
		     * function Foo() {
		     *   this.a = 1;
		     *   this.b = 2;
		     * }
		     *
		     * Foo.prototype.c = 3;
		     *
		     * _.valuesIn(new Foo);
		     * // => [1, 2, 3] (iteration order is not guaranteed)
		     */
		    function valuesIn(object) {
		      return object == null ? [] : baseValues(object, keysIn(object));
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Clamps `number` within the inclusive `lower` and `upper` bounds.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Number
		     * @param {number} number The number to clamp.
		     * @param {number} [lower] The lower bound.
		     * @param {number} upper The upper bound.
		     * @returns {number} Returns the clamped number.
		     * @example
		     *
		     * _.clamp(-10, -5, 5);
		     * // => -5
		     *
		     * _.clamp(10, -5, 5);
		     * // => 5
		     */
		    function clamp(number, lower, upper) {
		      if (upper === undefined$1) {
		        upper = lower;
		        lower = undefined$1;
		      }
		      if (upper !== undefined$1) {
		        upper = toNumber(upper);
		        upper = upper === upper ? upper : 0;
		      }
		      if (lower !== undefined$1) {
		        lower = toNumber(lower);
		        lower = lower === lower ? lower : 0;
		      }
		      return baseClamp(toNumber(number), lower, upper);
		    }

		    /**
		     * Checks if `n` is between `start` and up to, but not including, `end`. If
		     * `end` is not specified, it's set to `start` with `start` then set to `0`.
		     * If `start` is greater than `end` the params are swapped to support
		     * negative ranges.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.3.0
		     * @category Number
		     * @param {number} number The number to check.
		     * @param {number} [start=0] The start of the range.
		     * @param {number} end The end of the range.
		     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
		     * @see _.range, _.rangeRight
		     * @example
		     *
		     * _.inRange(3, 2, 4);
		     * // => true
		     *
		     * _.inRange(4, 8);
		     * // => true
		     *
		     * _.inRange(4, 2);
		     * // => false
		     *
		     * _.inRange(2, 2);
		     * // => false
		     *
		     * _.inRange(1.2, 2);
		     * // => true
		     *
		     * _.inRange(5.2, 4);
		     * // => false
		     *
		     * _.inRange(-3, -2, -6);
		     * // => true
		     */
		    function inRange(number, start, end) {
		      start = toFinite(start);
		      if (end === undefined$1) {
		        end = start;
		        start = 0;
		      } else {
		        end = toFinite(end);
		      }
		      number = toNumber(number);
		      return baseInRange(number, start, end);
		    }

		    /**
		     * Produces a random number between the inclusive `lower` and `upper` bounds.
		     * If only one argument is provided a number between `0` and the given number
		     * is returned. If `floating` is `true`, or either `lower` or `upper` are
		     * floats, a floating-point number is returned instead of an integer.
		     *
		     * **Note:** JavaScript follows the IEEE-754 standard for resolving
		     * floating-point values which can produce unexpected results.
		     *
		     * **Note:** If `lower` is greater than `upper`, the values are swapped.
		     *
		     * @static
		     * @memberOf _
		     * @since 0.7.0
		     * @category Number
		     * @param {number} [lower=0] The lower bound.
		     * @param {number} [upper=1] The upper bound.
		     * @param {boolean} [floating] Specify returning a floating-point number.
		     * @returns {number} Returns the random number.
		     * @example
		     *
		     * _.random(0, 5);
		     * // => an integer between 0 and 5
		     *
		     * // when lower is greater than upper the values are swapped
		     * _.random(5, 0);
		     * // => an integer between 0 and 5
		     *
		     * _.random(5);
		     * // => also an integer between 0 and 5
		     *
		     * _.random(-5);
		     * // => an integer between -5 and 0
		     *
		     * _.random(5, true);
		     * // => a floating-point number between 0 and 5
		     *
		     * _.random(1.2, 5.2);
		     * // => a floating-point number between 1.2 and 5.2
		     */
		    function random(lower, upper, floating) {
		      if (floating && typeof floating != 'boolean' && isIterateeCall(lower, upper, floating)) {
		        upper = floating = undefined$1;
		      }
		      if (floating === undefined$1) {
		        if (typeof upper == 'boolean') {
		          floating = upper;
		          upper = undefined$1;
		        }
		        else if (typeof lower == 'boolean') {
		          floating = lower;
		          lower = undefined$1;
		        }
		      }
		      if (lower === undefined$1 && upper === undefined$1) {
		        lower = 0;
		        upper = 1;
		      }
		      else {
		        lower = toFinite(lower);
		        if (upper === undefined$1) {
		          upper = lower;
		          lower = 0;
		        } else {
		          upper = toFinite(upper);
		        }
		      }
		      if (lower > upper) {
		        var temp = lower;
		        lower = upper;
		        upper = temp;
		      }
		      if (floating || lower % 1 || upper % 1) {
		        var rand = nativeRandom();
		        return nativeMin(lower + (rand * (upper - lower + freeParseFloat('1e-' + ((rand + '').length - 1)))), upper);
		      }
		      return baseRandom(lower, upper);
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Converts `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the camel cased string.
		     * @example
		     *
		     * _.camelCase('Foo Bar');
		     * // => 'fooBar'
		     *
		     * _.camelCase('--foo-bar--');
		     * // => 'fooBar'
		     *
		     * _.camelCase('__FOO_BAR__');
		     * // => 'fooBar'
		     */
		    var camelCase = createCompounder(function(result, word, index) {
		      word = word.toLowerCase();
		      return result + (index ? capitalize(word) : word);
		    });

		    /**
		     * Converts the first character of `string` to upper case and the remaining
		     * to lower case.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to capitalize.
		     * @returns {string} Returns the capitalized string.
		     * @example
		     *
		     * _.capitalize('FRED');
		     * // => 'Fred'
		     */
		    function capitalize(string) {
		      return upperFirst(toString(string).toLowerCase());
		    }

		    /**
		     * Deburrs `string` by converting
		     * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
		     * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
		     * letters to basic Latin letters and removing
		     * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to deburr.
		     * @returns {string} Returns the deburred string.
		     * @example
		     *
		     * _.deburr('déjà vu');
		     * // => 'deja vu'
		     */
		    function deburr(string) {
		      string = toString(string);
		      return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
		    }

		    /**
		     * Checks if `string` ends with the given target string.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to inspect.
		     * @param {string} [target] The string to search for.
		     * @param {number} [position=string.length] The position to search up to.
		     * @returns {boolean} Returns `true` if `string` ends with `target`,
		     *  else `false`.
		     * @example
		     *
		     * _.endsWith('abc', 'c');
		     * // => true
		     *
		     * _.endsWith('abc', 'b');
		     * // => false
		     *
		     * _.endsWith('abc', 'b', 2);
		     * // => true
		     */
		    function endsWith(string, target, position) {
		      string = toString(string);
		      target = baseToString(target);

		      var length = string.length;
		      position = position === undefined$1
		        ? length
		        : baseClamp(toInteger(position), 0, length);

		      var end = position;
		      position -= target.length;
		      return position >= 0 && string.slice(position, end) == target;
		    }

		    /**
		     * Converts the characters "&", "<", ">", '"', and "'" in `string` to their
		     * corresponding HTML entities.
		     *
		     * **Note:** No other characters are escaped. To escape additional
		     * characters use a third-party library like [_he_](https://mths.be/he).
		     *
		     * Though the ">" character is escaped for symmetry, characters like
		     * ">" and "/" don't need escaping in HTML and have no special meaning
		     * unless they're part of a tag or unquoted attribute value. See
		     * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
		     * (under "semi-related fun fact") for more details.
		     *
		     * When working with HTML you should always
		     * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
		     * XSS vectors.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category String
		     * @param {string} [string=''] The string to escape.
		     * @returns {string} Returns the escaped string.
		     * @example
		     *
		     * _.escape('fred, barney, & pebbles');
		     * // => 'fred, barney, &amp; pebbles'
		     */
		    function escape(string) {
		      string = toString(string);
		      return (string && reHasUnescapedHtml.test(string))
		        ? string.replace(reUnescapedHtml, escapeHtmlChar)
		        : string;
		    }

		    /**
		     * Escapes the `RegExp` special characters "^", "$", "\", ".", "*", "+",
		     * "?", "(", ")", "[", "]", "{", "}", and "|" in `string`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to escape.
		     * @returns {string} Returns the escaped string.
		     * @example
		     *
		     * _.escapeRegExp('[lodash](https://lodash.com/)');
		     * // => '\[lodash\]\(https://lodash\.com/\)'
		     */
		    function escapeRegExp(string) {
		      string = toString(string);
		      return (string && reHasRegExpChar.test(string))
		        ? string.replace(reRegExpChar, '\\$&')
		        : string;
		    }

		    /**
		     * Converts `string` to
		     * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the kebab cased string.
		     * @example
		     *
		     * _.kebabCase('Foo Bar');
		     * // => 'foo-bar'
		     *
		     * _.kebabCase('fooBar');
		     * // => 'foo-bar'
		     *
		     * _.kebabCase('__FOO_BAR__');
		     * // => 'foo-bar'
		     */
		    var kebabCase = createCompounder(function(result, word, index) {
		      return result + (index ? '-' : '') + word.toLowerCase();
		    });

		    /**
		     * Converts `string`, as space separated words, to lower case.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the lower cased string.
		     * @example
		     *
		     * _.lowerCase('--Foo-Bar--');
		     * // => 'foo bar'
		     *
		     * _.lowerCase('fooBar');
		     * // => 'foo bar'
		     *
		     * _.lowerCase('__FOO_BAR__');
		     * // => 'foo bar'
		     */
		    var lowerCase = createCompounder(function(result, word, index) {
		      return result + (index ? ' ' : '') + word.toLowerCase();
		    });

		    /**
		     * Converts the first character of `string` to lower case.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the converted string.
		     * @example
		     *
		     * _.lowerFirst('Fred');
		     * // => 'fred'
		     *
		     * _.lowerFirst('FRED');
		     * // => 'fRED'
		     */
		    var lowerFirst = createCaseFirst('toLowerCase');

		    /**
		     * Pads `string` on the left and right sides if it's shorter than `length`.
		     * Padding characters are truncated if they can't be evenly divided by `length`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to pad.
		     * @param {number} [length=0] The padding length.
		     * @param {string} [chars=' '] The string used as padding.
		     * @returns {string} Returns the padded string.
		     * @example
		     *
		     * _.pad('abc', 8);
		     * // => '  abc   '
		     *
		     * _.pad('abc', 8, '_-');
		     * // => '_-abc_-_'
		     *
		     * _.pad('abc', 3);
		     * // => 'abc'
		     */
		    function pad(string, length, chars) {
		      string = toString(string);
		      length = toInteger(length);

		      var strLength = length ? stringSize(string) : 0;
		      if (!length || strLength >= length) {
		        return string;
		      }
		      var mid = (length - strLength) / 2;
		      return (
		        createPadding(nativeFloor(mid), chars) +
		        string +
		        createPadding(nativeCeil(mid), chars)
		      );
		    }

		    /**
		     * Pads `string` on the right side if it's shorter than `length`. Padding
		     * characters are truncated if they exceed `length`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to pad.
		     * @param {number} [length=0] The padding length.
		     * @param {string} [chars=' '] The string used as padding.
		     * @returns {string} Returns the padded string.
		     * @example
		     *
		     * _.padEnd('abc', 6);
		     * // => 'abc   '
		     *
		     * _.padEnd('abc', 6, '_-');
		     * // => 'abc_-_'
		     *
		     * _.padEnd('abc', 3);
		     * // => 'abc'
		     */
		    function padEnd(string, length, chars) {
		      string = toString(string);
		      length = toInteger(length);

		      var strLength = length ? stringSize(string) : 0;
		      return (length && strLength < length)
		        ? (string + createPadding(length - strLength, chars))
		        : string;
		    }

		    /**
		     * Pads `string` on the left side if it's shorter than `length`. Padding
		     * characters are truncated if they exceed `length`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to pad.
		     * @param {number} [length=0] The padding length.
		     * @param {string} [chars=' '] The string used as padding.
		     * @returns {string} Returns the padded string.
		     * @example
		     *
		     * _.padStart('abc', 6);
		     * // => '   abc'
		     *
		     * _.padStart('abc', 6, '_-');
		     * // => '_-_abc'
		     *
		     * _.padStart('abc', 3);
		     * // => 'abc'
		     */
		    function padStart(string, length, chars) {
		      string = toString(string);
		      length = toInteger(length);

		      var strLength = length ? stringSize(string) : 0;
		      return (length && strLength < length)
		        ? (createPadding(length - strLength, chars) + string)
		        : string;
		    }

		    /**
		     * Converts `string` to an integer of the specified radix. If `radix` is
		     * `undefined` or `0`, a `radix` of `10` is used unless `value` is a
		     * hexadecimal, in which case a `radix` of `16` is used.
		     *
		     * **Note:** This method aligns with the
		     * [ES5 implementation](https://es5.github.io/#x15.1.2.2) of `parseInt`.
		     *
		     * @static
		     * @memberOf _
		     * @since 1.1.0
		     * @category String
		     * @param {string} string The string to convert.
		     * @param {number} [radix=10] The radix to interpret `value` by.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {number} Returns the converted integer.
		     * @example
		     *
		     * _.parseInt('08');
		     * // => 8
		     *
		     * _.map(['6', '08', '10'], _.parseInt);
		     * // => [6, 8, 10]
		     */
		    function parseInt(string, radix, guard) {
		      if (guard || radix == null) {
		        radix = 0;
		      } else if (radix) {
		        radix = +radix;
		      }
		      return nativeParseInt(toString(string).replace(reTrimStart, ''), radix || 0);
		    }

		    /**
		     * Repeats the given string `n` times.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to repeat.
		     * @param {number} [n=1] The number of times to repeat the string.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {string} Returns the repeated string.
		     * @example
		     *
		     * _.repeat('*', 3);
		     * // => '***'
		     *
		     * _.repeat('abc', 2);
		     * // => 'abcabc'
		     *
		     * _.repeat('abc', 0);
		     * // => ''
		     */
		    function repeat(string, n, guard) {
		      if ((guard ? isIterateeCall(string, n, guard) : n === undefined$1)) {
		        n = 1;
		      } else {
		        n = toInteger(n);
		      }
		      return baseRepeat(toString(string), n);
		    }

		    /**
		     * Replaces matches for `pattern` in `string` with `replacement`.
		     *
		     * **Note:** This method is based on
		     * [`String#replace`](https://mdn.io/String/replace).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to modify.
		     * @param {RegExp|string} pattern The pattern to replace.
		     * @param {Function|string} replacement The match replacement.
		     * @returns {string} Returns the modified string.
		     * @example
		     *
		     * _.replace('Hi Fred', 'Fred', 'Barney');
		     * // => 'Hi Barney'
		     */
		    function replace() {
		      var args = arguments,
		          string = toString(args[0]);

		      return args.length < 3 ? string : string.replace(args[1], args[2]);
		    }

		    /**
		     * Converts `string` to
		     * [snake case](https://en.wikipedia.org/wiki/Snake_case).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the snake cased string.
		     * @example
		     *
		     * _.snakeCase('Foo Bar');
		     * // => 'foo_bar'
		     *
		     * _.snakeCase('fooBar');
		     * // => 'foo_bar'
		     *
		     * _.snakeCase('--FOO-BAR--');
		     * // => 'foo_bar'
		     */
		    var snakeCase = createCompounder(function(result, word, index) {
		      return result + (index ? '_' : '') + word.toLowerCase();
		    });

		    /**
		     * Splits `string` by `separator`.
		     *
		     * **Note:** This method is based on
		     * [`String#split`](https://mdn.io/String/split).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to split.
		     * @param {RegExp|string} separator The separator pattern to split by.
		     * @param {number} [limit] The length to truncate results to.
		     * @returns {Array} Returns the string segments.
		     * @example
		     *
		     * _.split('a-b-c', '-', 2);
		     * // => ['a', 'b']
		     */
		    function split(string, separator, limit) {
		      if (limit && typeof limit != 'number' && isIterateeCall(string, separator, limit)) {
		        separator = limit = undefined$1;
		      }
		      limit = limit === undefined$1 ? MAX_ARRAY_LENGTH : limit >>> 0;
		      if (!limit) {
		        return [];
		      }
		      string = toString(string);
		      if (string && (
		            typeof separator == 'string' ||
		            (separator != null && !isRegExp(separator))
		          )) {
		        separator = baseToString(separator);
		        if (!separator && hasUnicode(string)) {
		          return castSlice(stringToArray(string), 0, limit);
		        }
		      }
		      return string.split(separator, limit);
		    }

		    /**
		     * Converts `string` to
		     * [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
		     *
		     * @static
		     * @memberOf _
		     * @since 3.1.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the start cased string.
		     * @example
		     *
		     * _.startCase('--foo-bar--');
		     * // => 'Foo Bar'
		     *
		     * _.startCase('fooBar');
		     * // => 'Foo Bar'
		     *
		     * _.startCase('__FOO_BAR__');
		     * // => 'FOO BAR'
		     */
		    var startCase = createCompounder(function(result, word, index) {
		      return result + (index ? ' ' : '') + upperFirst(word);
		    });

		    /**
		     * Checks if `string` starts with the given target string.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to inspect.
		     * @param {string} [target] The string to search for.
		     * @param {number} [position=0] The position to search from.
		     * @returns {boolean} Returns `true` if `string` starts with `target`,
		     *  else `false`.
		     * @example
		     *
		     * _.startsWith('abc', 'a');
		     * // => true
		     *
		     * _.startsWith('abc', 'b');
		     * // => false
		     *
		     * _.startsWith('abc', 'b', 1);
		     * // => true
		     */
		    function startsWith(string, target, position) {
		      string = toString(string);
		      position = position == null
		        ? 0
		        : baseClamp(toInteger(position), 0, string.length);

		      target = baseToString(target);
		      return string.slice(position, position + target.length) == target;
		    }

		    /**
		     * Creates a compiled template function that can interpolate data properties
		     * in "interpolate" delimiters, HTML-escape interpolated data properties in
		     * "escape" delimiters, and execute JavaScript in "evaluate" delimiters. Data
		     * properties may be accessed as free variables in the template. If a setting
		     * object is given, it takes precedence over `_.templateSettings` values.
		     *
		     * **Security:** `_.template` is insecure and should not be used. It will be
		     * removed in Lodash v5. Avoid untrusted input. See
		     * [threat model](https://github.com/lodash/lodash/blob/main/threat-model.md).
		     *
		     * **Note:** In the development build `_.template` utilizes
		     * [sourceURLs](http://www.html5rocks.com/en/tutorials/developertools/sourcemaps/#toc-sourceurl)
		     * for easier debugging.
		     *
		     * For more information on precompiling templates see
		     * [lodash's custom builds documentation](https://lodash.com/custom-builds).
		     *
		     * For more information on Chrome extension sandboxes see
		     * [Chrome's extensions documentation](https://developer.chrome.com/extensions/sandboxingEval).
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category String
		     * @param {string} [string=''] The template string.
		     * @param {Object} [options={}] The options object.
		     * @param {RegExp} [options.escape=_.templateSettings.escape]
		     *  The HTML "escape" delimiter.
		     * @param {RegExp} [options.evaluate=_.templateSettings.evaluate]
		     *  The "evaluate" delimiter.
		     * @param {Object} [options.imports=_.templateSettings.imports]
		     *  An object to import into the template as free variables.
		     * @param {RegExp} [options.interpolate=_.templateSettings.interpolate]
		     *  The "interpolate" delimiter.
		     * @param {string} [options.sourceURL='lodash.templateSources[n]']
		     *  The sourceURL of the compiled template.
		     * @param {string} [options.variable='obj']
		     *  The data object variable name.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Function} Returns the compiled template function.
		     * @example
		     *
		     * // Use the "interpolate" delimiter to create a compiled template.
		     * var compiled = _.template('hello <%= user %>!');
		     * compiled({ 'user': 'fred' });
		     * // => 'hello fred!'
		     *
		     * // Use the HTML "escape" delimiter to escape data property values.
		     * var compiled = _.template('<b><%- value %></b>');
		     * compiled({ 'value': '<script>' });
		     * // => '<b>&lt;script&gt;</b>'
		     *
		     * // Use the "evaluate" delimiter to execute JavaScript and generate HTML.
		     * var compiled = _.template('<% _.forEach(users, function(user) { %><li><%- user %></li><% }); %>');
		     * compiled({ 'users': ['fred', 'barney'] });
		     * // => '<li>fred</li><li>barney</li>'
		     *
		     * // Use the internal `print` function in "evaluate" delimiters.
		     * var compiled = _.template('<% print("hello " + user); %>!');
		     * compiled({ 'user': 'barney' });
		     * // => 'hello barney!'
		     *
		     * // Use the ES template literal delimiter as an "interpolate" delimiter.
		     * // Disable support by replacing the "interpolate" delimiter.
		     * var compiled = _.template('hello ${ user }!');
		     * compiled({ 'user': 'pebbles' });
		     * // => 'hello pebbles!'
		     *
		     * // Use backslashes to treat delimiters as plain text.
		     * var compiled = _.template('<%= "\\<%- value %\\>" %>');
		     * compiled({ 'value': 'ignored' });
		     * // => '<%- value %>'
		     *
		     * // Use the `imports` option to import `jQuery` as `jq`.
		     * var text = '<% jq.each(users, function(user) { %><li><%- user %></li><% }); %>';
		     * var compiled = _.template(text, { 'imports': { 'jq': jQuery } });
		     * compiled({ 'users': ['fred', 'barney'] });
		     * // => '<li>fred</li><li>barney</li>'
		     *
		     * // Use the `sourceURL` option to specify a custom sourceURL for the template.
		     * var compiled = _.template('hello <%= user %>!', { 'sourceURL': '/basic/greeting.jst' });
		     * compiled(data);
		     * // => Find the source of "greeting.jst" under the Sources tab or Resources panel of the web inspector.
		     *
		     * // Use the `variable` option to ensure a with-statement isn't used in the compiled template.
		     * var compiled = _.template('hi <%= data.user %>!', { 'variable': 'data' });
		     * compiled.source;
		     * // => function(data) {
		     * //   var __t, __p = '';
		     * //   __p += 'hi ' + ((__t = ( data.user )) == null ? '' : __t) + '!';
		     * //   return __p;
		     * // }
		     *
		     * // Use custom template delimiters.
		     * _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
		     * var compiled = _.template('hello {{ user }}!');
		     * compiled({ 'user': 'mustache' });
		     * // => 'hello mustache!'
		     *
		     * // Use the `source` property to inline compiled templates for meaningful
		     * // line numbers in error messages and stack traces.
		     * fs.writeFileSync(path.join(process.cwd(), 'jst.js'), '\
		     *   var JST = {\
		     *     "main": ' + _.template(mainText).source + '\
		     *   };\
		     * ');
		     */
		    function template(string, options, guard) {
		      // Based on John Resig's `tmpl` implementation
		      // (http://ejohn.org/blog/javascript-micro-templating/)
		      // and Laura Doktorova's doT.js (https://github.com/olado/doT).
		      var settings = lodash.templateSettings;

		      if (guard && isIterateeCall(string, options, guard)) {
		        options = undefined$1;
		      }
		      string = toString(string);
		      options = assignWith({}, options, settings, customDefaultsAssignIn);

		      var imports = assignWith({}, options.imports, settings.imports, customDefaultsAssignIn),
		          importsKeys = keys(imports),
		          importsValues = baseValues(imports, importsKeys);

		      arrayEach(importsKeys, function(key) {
		        if (reForbiddenIdentifierChars.test(key)) {
		          throw new Error(INVALID_TEMPL_IMPORTS_ERROR_TEXT);
		        }
		      });

		      var isEscaping,
		          isEvaluating,
		          index = 0,
		          interpolate = options.interpolate || reNoMatch,
		          source = "__p += '";

		      // Compile the regexp to match each delimiter.
		      var reDelimiters = RegExp(
		        (options.escape || reNoMatch).source + '|' +
		        interpolate.source + '|' +
		        (interpolate === reInterpolate ? reEsTemplate : reNoMatch).source + '|' +
		        (options.evaluate || reNoMatch).source + '|$'
		      , 'g');

		      // Use a sourceURL for easier debugging.
		      // The sourceURL gets injected into the source that's eval-ed, so be careful
		      // to normalize all kinds of whitespace, so e.g. newlines (and unicode versions of it) can't sneak in
		      // and escape the comment, thus injecting code that gets evaled.
		      var sourceURL = '//# sourceURL=' +
		        (hasOwnProperty.call(options, 'sourceURL')
		          ? (options.sourceURL + '').replace(/\s/g, ' ')
		          : ('lodash.templateSources[' + (++templateCounter) + ']')
		        ) + '\n';

		      string.replace(reDelimiters, function(match, escapeValue, interpolateValue, esTemplateValue, evaluateValue, offset) {
		        interpolateValue || (interpolateValue = esTemplateValue);

		        // Escape characters that can't be included in string literals.
		        source += string.slice(index, offset).replace(reUnescapedString, escapeStringChar);

		        // Replace delimiters with snippets.
		        if (escapeValue) {
		          isEscaping = true;
		          source += "' +\n__e(" + escapeValue + ") +\n'";
		        }
		        if (evaluateValue) {
		          isEvaluating = true;
		          source += "';\n" + evaluateValue + ";\n__p += '";
		        }
		        if (interpolateValue) {
		          source += "' +\n((__t = (" + interpolateValue + ")) == null ? '' : __t) +\n'";
		        }
		        index = offset + match.length;

		        // The JS engine embedded in Adobe products needs `match` returned in
		        // order to produce the correct `offset` value.
		        return match;
		      });

		      source += "';\n";

		      // If `variable` is not specified wrap a with-statement around the generated
		      // code to add the data object to the top of the scope chain.
		      var variable = hasOwnProperty.call(options, 'variable') && options.variable;
		      if (!variable) {
		        source = 'with (obj) {\n' + source + '\n}\n';
		      }
		      // Throw an error if a forbidden character was found in `variable`, to prevent
		      // potential command injection attacks.
		      else if (reForbiddenIdentifierChars.test(variable)) {
		        throw new Error(INVALID_TEMPL_VAR_ERROR_TEXT);
		      }

		      // Cleanup code by stripping empty strings.
		      source = (isEvaluating ? source.replace(reEmptyStringLeading, '') : source)
		        .replace(reEmptyStringMiddle, '$1')
		        .replace(reEmptyStringTrailing, '$1;');

		      // Frame code as the function body.
		      source = 'function(' + (variable || 'obj') + ') {\n' +
		        (variable
		          ? ''
		          : 'obj || (obj = {});\n'
		        ) +
		        "var __t, __p = ''" +
		        (isEscaping
		           ? ', __e = _.escape'
		           : ''
		        ) +
		        (isEvaluating
		          ? ', __j = Array.prototype.join;\n' +
		            "function print() { __p += __j.call(arguments, '') }\n"
		          : ';\n'
		        ) +
		        source +
		        'return __p\n}';

		      var result = attempt(function() {
		        return Function(importsKeys, sourceURL + 'return ' + source)
		          .apply(undefined$1, importsValues);
		      });

		      // Provide the compiled function's source by its `toString` method or
		      // the `source` property as a convenience for inlining compiled templates.
		      result.source = source;
		      if (isError(result)) {
		        throw result;
		      }
		      return result;
		    }

		    /**
		     * Converts `string`, as a whole, to lower case just like
		     * [String#toLowerCase](https://mdn.io/toLowerCase).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the lower cased string.
		     * @example
		     *
		     * _.toLower('--Foo-Bar--');
		     * // => '--foo-bar--'
		     *
		     * _.toLower('fooBar');
		     * // => 'foobar'
		     *
		     * _.toLower('__FOO_BAR__');
		     * // => '__foo_bar__'
		     */
		    function toLower(value) {
		      return toString(value).toLowerCase();
		    }

		    /**
		     * Converts `string`, as a whole, to upper case just like
		     * [String#toUpperCase](https://mdn.io/toUpperCase).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the upper cased string.
		     * @example
		     *
		     * _.toUpper('--foo-bar--');
		     * // => '--FOO-BAR--'
		     *
		     * _.toUpper('fooBar');
		     * // => 'FOOBAR'
		     *
		     * _.toUpper('__foo_bar__');
		     * // => '__FOO_BAR__'
		     */
		    function toUpper(value) {
		      return toString(value).toUpperCase();
		    }

		    /**
		     * Removes leading and trailing whitespace or specified characters from `string`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to trim.
		     * @param {string} [chars=whitespace] The characters to trim.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {string} Returns the trimmed string.
		     * @example
		     *
		     * _.trim('  abc  ');
		     * // => 'abc'
		     *
		     * _.trim('-_-abc-_-', '_-');
		     * // => 'abc'
		     *
		     * _.map(['  foo  ', '  bar  '], _.trim);
		     * // => ['foo', 'bar']
		     */
		    function trim(string, chars, guard) {
		      string = toString(string);
		      if (string && (guard || chars === undefined$1)) {
		        return baseTrim(string);
		      }
		      if (!string || !(chars = baseToString(chars))) {
		        return string;
		      }
		      var strSymbols = stringToArray(string),
		          chrSymbols = stringToArray(chars),
		          start = charsStartIndex(strSymbols, chrSymbols),
		          end = charsEndIndex(strSymbols, chrSymbols) + 1;

		      return castSlice(strSymbols, start, end).join('');
		    }

		    /**
		     * Removes trailing whitespace or specified characters from `string`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to trim.
		     * @param {string} [chars=whitespace] The characters to trim.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {string} Returns the trimmed string.
		     * @example
		     *
		     * _.trimEnd('  abc  ');
		     * // => '  abc'
		     *
		     * _.trimEnd('-_-abc-_-', '_-');
		     * // => '-_-abc'
		     */
		    function trimEnd(string, chars, guard) {
		      string = toString(string);
		      if (string && (guard || chars === undefined$1)) {
		        return string.slice(0, trimmedEndIndex(string) + 1);
		      }
		      if (!string || !(chars = baseToString(chars))) {
		        return string;
		      }
		      var strSymbols = stringToArray(string),
		          end = charsEndIndex(strSymbols, stringToArray(chars)) + 1;

		      return castSlice(strSymbols, 0, end).join('');
		    }

		    /**
		     * Removes leading whitespace or specified characters from `string`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to trim.
		     * @param {string} [chars=whitespace] The characters to trim.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {string} Returns the trimmed string.
		     * @example
		     *
		     * _.trimStart('  abc  ');
		     * // => 'abc  '
		     *
		     * _.trimStart('-_-abc-_-', '_-');
		     * // => 'abc-_-'
		     */
		    function trimStart(string, chars, guard) {
		      string = toString(string);
		      if (string && (guard || chars === undefined$1)) {
		        return string.replace(reTrimStart, '');
		      }
		      if (!string || !(chars = baseToString(chars))) {
		        return string;
		      }
		      var strSymbols = stringToArray(string),
		          start = charsStartIndex(strSymbols, stringToArray(chars));

		      return castSlice(strSymbols, start).join('');
		    }

		    /**
		     * Truncates `string` if it's longer than the given maximum string length.
		     * The last characters of the truncated string are replaced with the omission
		     * string which defaults to "...".
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to truncate.
		     * @param {Object} [options={}] The options object.
		     * @param {number} [options.length=30] The maximum string length.
		     * @param {string} [options.omission='...'] The string to indicate text is omitted.
		     * @param {RegExp|string} [options.separator] The separator pattern to truncate to.
		     * @returns {string} Returns the truncated string.
		     * @example
		     *
		     * _.truncate('hi-diddly-ho there, neighborino');
		     * // => 'hi-diddly-ho there, neighbo...'
		     *
		     * _.truncate('hi-diddly-ho there, neighborino', {
		     *   'length': 24,
		     *   'separator': ' '
		     * });
		     * // => 'hi-diddly-ho there,...'
		     *
		     * _.truncate('hi-diddly-ho there, neighborino', {
		     *   'length': 24,
		     *   'separator': /,? +/
		     * });
		     * // => 'hi-diddly-ho there...'
		     *
		     * _.truncate('hi-diddly-ho there, neighborino', {
		     *   'omission': ' [...]'
		     * });
		     * // => 'hi-diddly-ho there, neig [...]'
		     */
		    function truncate(string, options) {
		      var length = DEFAULT_TRUNC_LENGTH,
		          omission = DEFAULT_TRUNC_OMISSION;

		      if (isObject(options)) {
		        var separator = 'separator' in options ? options.separator : separator;
		        length = 'length' in options ? toInteger(options.length) : length;
		        omission = 'omission' in options ? baseToString(options.omission) : omission;
		      }
		      string = toString(string);

		      var strLength = string.length;
		      if (hasUnicode(string)) {
		        var strSymbols = stringToArray(string);
		        strLength = strSymbols.length;
		      }
		      if (length >= strLength) {
		        return string;
		      }
		      var end = length - stringSize(omission);
		      if (end < 1) {
		        return omission;
		      }
		      var result = strSymbols
		        ? castSlice(strSymbols, 0, end).join('')
		        : string.slice(0, end);

		      if (separator === undefined$1) {
		        return result + omission;
		      }
		      if (strSymbols) {
		        end += (result.length - end);
		      }
		      if (isRegExp(separator)) {
		        if (string.slice(end).search(separator)) {
		          var match,
		              substring = result;

		          if (!separator.global) {
		            separator = RegExp(separator.source, toString(reFlags.exec(separator)) + 'g');
		          }
		          separator.lastIndex = 0;
		          while ((match = separator.exec(substring))) {
		            var newEnd = match.index;
		          }
		          result = result.slice(0, newEnd === undefined$1 ? end : newEnd);
		        }
		      } else if (string.indexOf(baseToString(separator), end) != end) {
		        var index = result.lastIndexOf(separator);
		        if (index > -1) {
		          result = result.slice(0, index);
		        }
		      }
		      return result + omission;
		    }

		    /**
		     * The inverse of `_.escape`; this method converts the HTML entities
		     * `&amp;`, `&lt;`, `&gt;`, `&quot;`, and `&#39;` in `string` to
		     * their corresponding characters.
		     *
		     * **Note:** No other HTML entities are unescaped. To unescape additional
		     * HTML entities use a third-party library like [_he_](https://mths.be/he).
		     *
		     * @static
		     * @memberOf _
		     * @since 0.6.0
		     * @category String
		     * @param {string} [string=''] The string to unescape.
		     * @returns {string} Returns the unescaped string.
		     * @example
		     *
		     * _.unescape('fred, barney, &amp; pebbles');
		     * // => 'fred, barney, & pebbles'
		     */
		    function unescape(string) {
		      string = toString(string);
		      return (string && reHasEscapedHtml.test(string))
		        ? string.replace(reEscapedHtml, unescapeHtmlChar)
		        : string;
		    }

		    /**
		     * Converts `string`, as space separated words, to upper case.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the upper cased string.
		     * @example
		     *
		     * _.upperCase('--foo-bar');
		     * // => 'FOO BAR'
		     *
		     * _.upperCase('fooBar');
		     * // => 'FOO BAR'
		     *
		     * _.upperCase('__foo_bar__');
		     * // => 'FOO BAR'
		     */
		    var upperCase = createCompounder(function(result, word, index) {
		      return result + (index ? ' ' : '') + word.toUpperCase();
		    });

		    /**
		     * Converts the first character of `string` to upper case.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category String
		     * @param {string} [string=''] The string to convert.
		     * @returns {string} Returns the converted string.
		     * @example
		     *
		     * _.upperFirst('fred');
		     * // => 'Fred'
		     *
		     * _.upperFirst('FRED');
		     * // => 'FRED'
		     */
		    var upperFirst = createCaseFirst('toUpperCase');

		    /**
		     * Splits `string` into an array of its words.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category String
		     * @param {string} [string=''] The string to inspect.
		     * @param {RegExp|string} [pattern] The pattern to match words.
		     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
		     * @returns {Array} Returns the words of `string`.
		     * @example
		     *
		     * _.words('fred, barney, & pebbles');
		     * // => ['fred', 'barney', 'pebbles']
		     *
		     * _.words('fred, barney, & pebbles', /[^, ]+/g);
		     * // => ['fred', 'barney', '&', 'pebbles']
		     */
		    function words(string, pattern, guard) {
		      string = toString(string);
		      pattern = guard ? undefined$1 : pattern;

		      if (pattern === undefined$1) {
		        return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
		      }
		      return string.match(pattern) || [];
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Attempts to invoke `func`, returning either the result or the caught error
		     * object. Any additional arguments are provided to `func` when it's invoked.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Util
		     * @param {Function} func The function to attempt.
		     * @param {...*} [args] The arguments to invoke `func` with.
		     * @returns {*} Returns the `func` result or error object.
		     * @example
		     *
		     * // Avoid throwing errors for invalid selectors.
		     * var elements = _.attempt(function(selector) {
		     *   return document.querySelectorAll(selector);
		     * }, '>_>');
		     *
		     * if (_.isError(elements)) {
		     *   elements = [];
		     * }
		     */
		    var attempt = baseRest(function(func, args) {
		      try {
		        return apply(func, undefined$1, args);
		      } catch (e) {
		        return isError(e) ? e : new Error(e);
		      }
		    });

		    /**
		     * Binds methods of an object to the object itself, overwriting the existing
		     * method.
		     *
		     * **Note:** This method doesn't set the "length" property of bound functions.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @param {Object} object The object to bind and assign the bound methods to.
		     * @param {...(string|string[])} methodNames The object method names to bind.
		     * @returns {Object} Returns `object`.
		     * @example
		     *
		     * var view = {
		     *   'label': 'docs',
		     *   'click': function() {
		     *     console.log('clicked ' + this.label);
		     *   }
		     * };
		     *
		     * _.bindAll(view, ['click']);
		     * jQuery(element).on('click', view.click);
		     * // => Logs 'clicked docs' when clicked.
		     */
		    var bindAll = flatRest(function(object, methodNames) {
		      arrayEach(methodNames, function(key) {
		        key = toKey(key);
		        baseAssignValue(object, key, bind(object[key], object));
		      });
		      return object;
		    });

		    /**
		     * Creates a function that iterates over `pairs` and invokes the corresponding
		     * function of the first predicate to return truthy. The predicate-function
		     * pairs are invoked with the `this` binding and arguments of the created
		     * function.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {Array} pairs The predicate-function pairs.
		     * @returns {Function} Returns the new composite function.
		     * @example
		     *
		     * var func = _.cond([
		     *   [_.matches({ 'a': 1 }),           _.constant('matches A')],
		     *   [_.conforms({ 'b': _.isNumber }), _.constant('matches B')],
		     *   [_.stubTrue,                      _.constant('no match')]
		     * ]);
		     *
		     * func({ 'a': 1, 'b': 2 });
		     * // => 'matches A'
		     *
		     * func({ 'a': 0, 'b': 1 });
		     * // => 'matches B'
		     *
		     * func({ 'a': '1', 'b': '2' });
		     * // => 'no match'
		     */
		    function cond(pairs) {
		      var length = pairs == null ? 0 : pairs.length,
		          toIteratee = getIteratee();

		      pairs = !length ? [] : arrayMap(pairs, function(pair) {
		        if (typeof pair[1] != 'function') {
		          throw new TypeError(FUNC_ERROR_TEXT);
		        }
		        return [toIteratee(pair[0]), pair[1]];
		      });

		      return baseRest(function(args) {
		        var index = -1;
		        while (++index < length) {
		          var pair = pairs[index];
		          if (apply(pair[0], this, args)) {
		            return apply(pair[1], this, args);
		          }
		        }
		      });
		    }

		    /**
		     * Creates a function that invokes the predicate properties of `source` with
		     * the corresponding property values of a given object, returning `true` if
		     * all predicates return truthy, else `false`.
		     *
		     * **Note:** The created function is equivalent to `_.conformsTo` with
		     * `source` partially applied.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {Object} source The object of property predicates to conform to.
		     * @returns {Function} Returns the new spec function.
		     * @example
		     *
		     * var objects = [
		     *   { 'a': 2, 'b': 1 },
		     *   { 'a': 1, 'b': 2 }
		     * ];
		     *
		     * _.filter(objects, _.conforms({ 'b': function(n) { return n > 1; } }));
		     * // => [{ 'a': 1, 'b': 2 }]
		     */
		    function conforms(source) {
		      return baseConforms(baseClone(source, CLONE_DEEP_FLAG));
		    }

		    /**
		     * Creates a function that returns `value`.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.4.0
		     * @category Util
		     * @param {*} value The value to return from the new function.
		     * @returns {Function} Returns the new constant function.
		     * @example
		     *
		     * var objects = _.times(2, _.constant({ 'a': 1 }));
		     *
		     * console.log(objects);
		     * // => [{ 'a': 1 }, { 'a': 1 }]
		     *
		     * console.log(objects[0] === objects[1]);
		     * // => true
		     */
		    function constant(value) {
		      return function() {
		        return value;
		      };
		    }

		    /**
		     * Checks `value` to determine whether a default value should be returned in
		     * its place. The `defaultValue` is returned if `value` is `NaN`, `null`,
		     * or `undefined`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.14.0
		     * @category Util
		     * @param {*} value The value to check.
		     * @param {*} defaultValue The default value.
		     * @returns {*} Returns the resolved value.
		     * @example
		     *
		     * _.defaultTo(1, 10);
		     * // => 1
		     *
		     * _.defaultTo(undefined, 10);
		     * // => 10
		     */
		    function defaultTo(value, defaultValue) {
		      return (value == null || value !== value) ? defaultValue : value;
		    }

		    /**
		     * Creates a function that returns the result of invoking the given functions
		     * with the `this` binding of the created function, where each successive
		     * invocation is supplied the return value of the previous.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Util
		     * @param {...(Function|Function[])} [funcs] The functions to invoke.
		     * @returns {Function} Returns the new composite function.
		     * @see _.flowRight
		     * @example
		     *
		     * function square(n) {
		     *   return n * n;
		     * }
		     *
		     * var addSquare = _.flow([_.add, square]);
		     * addSquare(1, 2);
		     * // => 9
		     */
		    var flow = createFlow();

		    /**
		     * This method is like `_.flow` except that it creates a function that
		     * invokes the given functions from right to left.
		     *
		     * @static
		     * @since 3.0.0
		     * @memberOf _
		     * @category Util
		     * @param {...(Function|Function[])} [funcs] The functions to invoke.
		     * @returns {Function} Returns the new composite function.
		     * @see _.flow
		     * @example
		     *
		     * function square(n) {
		     *   return n * n;
		     * }
		     *
		     * var addSquare = _.flowRight([square, _.add]);
		     * addSquare(1, 2);
		     * // => 9
		     */
		    var flowRight = createFlow(true);

		    /**
		     * This method returns the first argument it receives.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @param {*} value Any value.
		     * @returns {*} Returns `value`.
		     * @example
		     *
		     * var object = { 'a': 1 };
		     *
		     * console.log(_.identity(object) === object);
		     * // => true
		     */
		    function identity(value) {
		      return value;
		    }

		    /**
		     * Creates a function that invokes `func` with the arguments of the created
		     * function. If `func` is a property name, the created function returns the
		     * property value for a given element. If `func` is an array or object, the
		     * created function returns `true` for elements that contain the equivalent
		     * source properties, otherwise it returns `false`.
		     *
		     * @static
		     * @since 4.0.0
		     * @memberOf _
		     * @category Util
		     * @param {*} [func=_.identity] The value to convert to a callback.
		     * @returns {Function} Returns the callback.
		     * @example
		     *
		     * var users = [
		     *   { 'user': 'barney', 'age': 36, 'active': true },
		     *   { 'user': 'fred',   'age': 40, 'active': false }
		     * ];
		     *
		     * // The `_.matches` iteratee shorthand.
		     * _.filter(users, _.iteratee({ 'user': 'barney', 'active': true }));
		     * // => [{ 'user': 'barney', 'age': 36, 'active': true }]
		     *
		     * // The `_.matchesProperty` iteratee shorthand.
		     * _.filter(users, _.iteratee(['user', 'fred']));
		     * // => [{ 'user': 'fred', 'age': 40 }]
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.map(users, _.iteratee('user'));
		     * // => ['barney', 'fred']
		     *
		     * // Create custom iteratee shorthands.
		     * _.iteratee = _.wrap(_.iteratee, function(iteratee, func) {
		     *   return !_.isRegExp(func) ? iteratee(func) : function(string) {
		     *     return func.test(string);
		     *   };
		     * });
		     *
		     * _.filter(['abc', 'def'], /ef/);
		     * // => ['def']
		     */
		    function iteratee(func) {
		      return baseIteratee(typeof func == 'function' ? func : baseClone(func, CLONE_DEEP_FLAG));
		    }

		    /**
		     * Creates a function that performs a partial deep comparison between a given
		     * object and `source`, returning `true` if the given object has equivalent
		     * property values, else `false`.
		     *
		     * **Note:** The created function is equivalent to `_.isMatch` with `source`
		     * partially applied.
		     *
		     * Partial comparisons will match empty array and empty object `source`
		     * values against any array or object value, respectively. See `_.isEqual`
		     * for a list of supported value comparisons.
		     *
		     * **Note:** Multiple values can be checked by combining several matchers
		     * using `_.overSome`
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Util
		     * @param {Object} source The object of property values to match.
		     * @returns {Function} Returns the new spec function.
		     * @example
		     *
		     * var objects = [
		     *   { 'a': 1, 'b': 2, 'c': 3 },
		     *   { 'a': 4, 'b': 5, 'c': 6 }
		     * ];
		     *
		     * _.filter(objects, _.matches({ 'a': 4, 'c': 6 }));
		     * // => [{ 'a': 4, 'b': 5, 'c': 6 }]
		     *
		     * // Checking for several possible values
		     * _.filter(objects, _.overSome([_.matches({ 'a': 1 }), _.matches({ 'a': 4 })]));
		     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
		     */
		    function matches(source) {
		      return baseMatches(baseClone(source, CLONE_DEEP_FLAG));
		    }

		    /**
		     * Creates a function that performs a partial deep comparison between the
		     * value at `path` of a given object to `srcValue`, returning `true` if the
		     * object value is equivalent, else `false`.
		     *
		     * **Note:** Partial comparisons will match empty array and empty object
		     * `srcValue` values against any array or object value, respectively. See
		     * `_.isEqual` for a list of supported value comparisons.
		     *
		     * **Note:** Multiple values can be checked by combining several matchers
		     * using `_.overSome`
		     *
		     * @static
		     * @memberOf _
		     * @since 3.2.0
		     * @category Util
		     * @param {Array|string} path The path of the property to get.
		     * @param {*} srcValue The value to match.
		     * @returns {Function} Returns the new spec function.
		     * @example
		     *
		     * var objects = [
		     *   { 'a': 1, 'b': 2, 'c': 3 },
		     *   { 'a': 4, 'b': 5, 'c': 6 }
		     * ];
		     *
		     * _.find(objects, _.matchesProperty('a', 4));
		     * // => { 'a': 4, 'b': 5, 'c': 6 }
		     *
		     * // Checking for several possible values
		     * _.filter(objects, _.overSome([_.matchesProperty('a', 1), _.matchesProperty('a', 4)]));
		     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
		     */
		    function matchesProperty(path, srcValue) {
		      return baseMatchesProperty(path, baseClone(srcValue, CLONE_DEEP_FLAG));
		    }

		    /**
		     * Creates a function that invokes the method at `path` of a given object.
		     * Any additional arguments are provided to the invoked method.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.7.0
		     * @category Util
		     * @param {Array|string} path The path of the method to invoke.
		     * @param {...*} [args] The arguments to invoke the method with.
		     * @returns {Function} Returns the new invoker function.
		     * @example
		     *
		     * var objects = [
		     *   { 'a': { 'b': _.constant(2) } },
		     *   { 'a': { 'b': _.constant(1) } }
		     * ];
		     *
		     * _.map(objects, _.method('a.b'));
		     * // => [2, 1]
		     *
		     * _.map(objects, _.method(['a', 'b']));
		     * // => [2, 1]
		     */
		    var method = baseRest(function(path, args) {
		      return function(object) {
		        return baseInvoke(object, path, args);
		      };
		    });

		    /**
		     * The opposite of `_.method`; this method creates a function that invokes
		     * the method at a given path of `object`. Any additional arguments are
		     * provided to the invoked method.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.7.0
		     * @category Util
		     * @param {Object} object The object to query.
		     * @param {...*} [args] The arguments to invoke the method with.
		     * @returns {Function} Returns the new invoker function.
		     * @example
		     *
		     * var array = _.times(3, _.constant),
		     *     object = { 'a': array, 'b': array, 'c': array };
		     *
		     * _.map(['a[2]', 'c[0]'], _.methodOf(object));
		     * // => [2, 0]
		     *
		     * _.map([['a', '2'], ['c', '0']], _.methodOf(object));
		     * // => [2, 0]
		     */
		    var methodOf = baseRest(function(object, args) {
		      return function(path) {
		        return baseInvoke(object, path, args);
		      };
		    });

		    /**
		     * Adds all own enumerable string keyed function properties of a source
		     * object to the destination object. If `object` is a function, then methods
		     * are added to its prototype as well.
		     *
		     * **Note:** Use `_.runInContext` to create a pristine `lodash` function to
		     * avoid conflicts caused by modifying the original.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @param {Function|Object} [object=lodash] The destination object.
		     * @param {Object} source The object of functions to add.
		     * @param {Object} [options={}] The options object.
		     * @param {boolean} [options.chain=true] Specify whether mixins are chainable.
		     * @returns {Function|Object} Returns `object`.
		     * @example
		     *
		     * function vowels(string) {
		     *   return _.filter(string, function(v) {
		     *     return /[aeiou]/i.test(v);
		     *   });
		     * }
		     *
		     * _.mixin({ 'vowels': vowels });
		     * _.vowels('fred');
		     * // => ['e']
		     *
		     * _('fred').vowels().value();
		     * // => ['e']
		     *
		     * _.mixin({ 'vowels': vowels }, { 'chain': false });
		     * _('fred').vowels();
		     * // => ['e']
		     */
		    function mixin(object, source, options) {
		      var props = keys(source),
		          methodNames = baseFunctions(source, props);

		      if (options == null &&
		          !(isObject(source) && (methodNames.length || !props.length))) {
		        options = source;
		        source = object;
		        object = this;
		        methodNames = baseFunctions(source, keys(source));
		      }
		      var chain = !(isObject(options) && 'chain' in options) || !!options.chain,
		          isFunc = isFunction(object);

		      arrayEach(methodNames, function(methodName) {
		        var func = source[methodName];
		        object[methodName] = func;
		        if (isFunc) {
		          object.prototype[methodName] = function() {
		            var chainAll = this.__chain__;
		            if (chain || chainAll) {
		              var result = object(this.__wrapped__),
		                  actions = result.__actions__ = copyArray(this.__actions__);

		              actions.push({ 'func': func, 'args': arguments, 'thisArg': object });
		              result.__chain__ = chainAll;
		              return result;
		            }
		            return func.apply(object, arrayPush([this.value()], arguments));
		          };
		        }
		      });

		      return object;
		    }

		    /**
		     * Reverts the `_` variable to its previous value and returns a reference to
		     * the `lodash` function.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @returns {Function} Returns the `lodash` function.
		     * @example
		     *
		     * var lodash = _.noConflict();
		     */
		    function noConflict() {
		      if (root._ === this) {
		        root._ = oldDash;
		      }
		      return this;
		    }

		    /**
		     * This method returns `undefined`.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.3.0
		     * @category Util
		     * @example
		     *
		     * _.times(2, _.noop);
		     * // => [undefined, undefined]
		     */
		    function noop() {
		      // No operation performed.
		    }

		    /**
		     * Creates a function that gets the argument at index `n`. If `n` is negative,
		     * the nth argument from the end is returned.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {number} [n=0] The index of the argument to return.
		     * @returns {Function} Returns the new pass-thru function.
		     * @example
		     *
		     * var func = _.nthArg(1);
		     * func('a', 'b', 'c', 'd');
		     * // => 'b'
		     *
		     * var func = _.nthArg(-2);
		     * func('a', 'b', 'c', 'd');
		     * // => 'c'
		     */
		    function nthArg(n) {
		      n = toInteger(n);
		      return baseRest(function(args) {
		        return baseNth(args, n);
		      });
		    }

		    /**
		     * Creates a function that invokes `iteratees` with the arguments it receives
		     * and returns their results.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {...(Function|Function[])} [iteratees=[_.identity]]
		     *  The iteratees to invoke.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var func = _.over([Math.max, Math.min]);
		     *
		     * func(1, 2, 3, 4);
		     * // => [4, 1]
		     */
		    var over = createOver(arrayMap);

		    /**
		     * Creates a function that checks if **all** of the `predicates` return
		     * truthy when invoked with the arguments it receives.
		     *
		     * Following shorthands are possible for providing predicates.
		     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
		     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {...(Function|Function[])} [predicates=[_.identity]]
		     *  The predicates to check.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var func = _.overEvery([Boolean, isFinite]);
		     *
		     * func('1');
		     * // => true
		     *
		     * func(null);
		     * // => false
		     *
		     * func(NaN);
		     * // => false
		     */
		    var overEvery = createOver(arrayEvery);

		    /**
		     * Creates a function that checks if **any** of the `predicates` return
		     * truthy when invoked with the arguments it receives.
		     *
		     * Following shorthands are possible for providing predicates.
		     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
		     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {...(Function|Function[])} [predicates=[_.identity]]
		     *  The predicates to check.
		     * @returns {Function} Returns the new function.
		     * @example
		     *
		     * var func = _.overSome([Boolean, isFinite]);
		     *
		     * func('1');
		     * // => true
		     *
		     * func(null);
		     * // => true
		     *
		     * func(NaN);
		     * // => false
		     *
		     * var matchesFunc = _.overSome([{ 'a': 1 }, { 'a': 2 }])
		     * var matchesPropertyFunc = _.overSome([['a', 1], ['a', 2]])
		     */
		    var overSome = createOver(arraySome);

		    /**
		     * Creates a function that returns the value at `path` of a given object.
		     *
		     * @static
		     * @memberOf _
		     * @since 2.4.0
		     * @category Util
		     * @param {Array|string} path The path of the property to get.
		     * @returns {Function} Returns the new accessor function.
		     * @example
		     *
		     * var objects = [
		     *   { 'a': { 'b': 2 } },
		     *   { 'a': { 'b': 1 } }
		     * ];
		     *
		     * _.map(objects, _.property('a.b'));
		     * // => [2, 1]
		     *
		     * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
		     * // => [1, 2]
		     */
		    function property(path) {
		      return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
		    }

		    /**
		     * The opposite of `_.property`; this method creates a function that returns
		     * the value at a given path of `object`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.0.0
		     * @category Util
		     * @param {Object} object The object to query.
		     * @returns {Function} Returns the new accessor function.
		     * @example
		     *
		     * var array = [0, 1, 2],
		     *     object = { 'a': array, 'b': array, 'c': array };
		     *
		     * _.map(['a[2]', 'c[0]'], _.propertyOf(object));
		     * // => [2, 0]
		     *
		     * _.map([['a', '2'], ['c', '0']], _.propertyOf(object));
		     * // => [2, 0]
		     */
		    function propertyOf(object) {
		      return function(path) {
		        return object == null ? undefined$1 : baseGet(object, path);
		      };
		    }

		    /**
		     * Creates an array of numbers (positive and/or negative) progressing from
		     * `start` up to, but not including, `end`. A step of `-1` is used if a negative
		     * `start` is specified without an `end` or `step`. If `end` is not specified,
		     * it's set to `start` with `start` then set to `0`.
		     *
		     * **Note:** JavaScript follows the IEEE-754 standard for resolving
		     * floating-point values which can produce unexpected results.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @param {number} [start=0] The start of the range.
		     * @param {number} end The end of the range.
		     * @param {number} [step=1] The value to increment or decrement by.
		     * @returns {Array} Returns the range of numbers.
		     * @see _.inRange, _.rangeRight
		     * @example
		     *
		     * _.range(4);
		     * // => [0, 1, 2, 3]
		     *
		     * _.range(-4);
		     * // => [0, -1, -2, -3]
		     *
		     * _.range(1, 5);
		     * // => [1, 2, 3, 4]
		     *
		     * _.range(0, 20, 5);
		     * // => [0, 5, 10, 15]
		     *
		     * _.range(0, -4, -1);
		     * // => [0, -1, -2, -3]
		     *
		     * _.range(1, 4, 0);
		     * // => [1, 1, 1]
		     *
		     * _.range(0);
		     * // => []
		     */
		    var range = createRange();

		    /**
		     * This method is like `_.range` except that it populates values in
		     * descending order.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {number} [start=0] The start of the range.
		     * @param {number} end The end of the range.
		     * @param {number} [step=1] The value to increment or decrement by.
		     * @returns {Array} Returns the range of numbers.
		     * @see _.inRange, _.range
		     * @example
		     *
		     * _.rangeRight(4);
		     * // => [3, 2, 1, 0]
		     *
		     * _.rangeRight(-4);
		     * // => [-3, -2, -1, 0]
		     *
		     * _.rangeRight(1, 5);
		     * // => [4, 3, 2, 1]
		     *
		     * _.rangeRight(0, 20, 5);
		     * // => [15, 10, 5, 0]
		     *
		     * _.rangeRight(0, -4, -1);
		     * // => [-3, -2, -1, 0]
		     *
		     * _.rangeRight(1, 4, 0);
		     * // => [1, 1, 1]
		     *
		     * _.rangeRight(0);
		     * // => []
		     */
		    var rangeRight = createRange(true);

		    /**
		     * This method returns a new empty array.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.13.0
		     * @category Util
		     * @returns {Array} Returns the new empty array.
		     * @example
		     *
		     * var arrays = _.times(2, _.stubArray);
		     *
		     * console.log(arrays);
		     * // => [[], []]
		     *
		     * console.log(arrays[0] === arrays[1]);
		     * // => false
		     */
		    function stubArray() {
		      return [];
		    }

		    /**
		     * This method returns `false`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.13.0
		     * @category Util
		     * @returns {boolean} Returns `false`.
		     * @example
		     *
		     * _.times(2, _.stubFalse);
		     * // => [false, false]
		     */
		    function stubFalse() {
		      return false;
		    }

		    /**
		     * This method returns a new empty object.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.13.0
		     * @category Util
		     * @returns {Object} Returns the new empty object.
		     * @example
		     *
		     * var objects = _.times(2, _.stubObject);
		     *
		     * console.log(objects);
		     * // => [{}, {}]
		     *
		     * console.log(objects[0] === objects[1]);
		     * // => false
		     */
		    function stubObject() {
		      return {};
		    }

		    /**
		     * This method returns an empty string.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.13.0
		     * @category Util
		     * @returns {string} Returns the empty string.
		     * @example
		     *
		     * _.times(2, _.stubString);
		     * // => ['', '']
		     */
		    function stubString() {
		      return '';
		    }

		    /**
		     * This method returns `true`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.13.0
		     * @category Util
		     * @returns {boolean} Returns `true`.
		     * @example
		     *
		     * _.times(2, _.stubTrue);
		     * // => [true, true]
		     */
		    function stubTrue() {
		      return true;
		    }

		    /**
		     * Invokes the iteratee `n` times, returning an array of the results of
		     * each invocation. The iteratee is invoked with one argument; (index).
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @param {number} n The number of times to invoke `iteratee`.
		     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
		     * @returns {Array} Returns the array of results.
		     * @example
		     *
		     * _.times(3, String);
		     * // => ['0', '1', '2']
		     *
		     *  _.times(4, _.constant(0));
		     * // => [0, 0, 0, 0]
		     */
		    function times(n, iteratee) {
		      n = toInteger(n);
		      if (n < 1 || n > MAX_SAFE_INTEGER) {
		        return [];
		      }
		      var index = MAX_ARRAY_LENGTH,
		          length = nativeMin(n, MAX_ARRAY_LENGTH);

		      iteratee = getIteratee(iteratee);
		      n -= MAX_ARRAY_LENGTH;

		      var result = baseTimes(length, iteratee);
		      while (++index < n) {
		        iteratee(index);
		      }
		      return result;
		    }

		    /**
		     * Converts `value` to a property path array.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Util
		     * @param {*} value The value to convert.
		     * @returns {Array} Returns the new property path array.
		     * @example
		     *
		     * _.toPath('a.b.c');
		     * // => ['a', 'b', 'c']
		     *
		     * _.toPath('a[0].b.c');
		     * // => ['a', '0', 'b', 'c']
		     */
		    function toPath(value) {
		      if (isArray(value)) {
		        return arrayMap(value, toKey);
		      }
		      return isSymbol(value) ? [value] : copyArray(stringToPath(toString(value)));
		    }

		    /**
		     * Generates a unique ID. If `prefix` is given, the ID is appended to it.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Util
		     * @param {string} [prefix=''] The value to prefix the ID with.
		     * @returns {string} Returns the unique ID.
		     * @example
		     *
		     * _.uniqueId('contact_');
		     * // => 'contact_104'
		     *
		     * _.uniqueId();
		     * // => '105'
		     */
		    function uniqueId(prefix) {
		      var id = ++idCounter;
		      return toString(prefix) + id;
		    }

		    /*------------------------------------------------------------------------*/

		    /**
		     * Adds two numbers.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.4.0
		     * @category Math
		     * @param {number} augend The first number in an addition.
		     * @param {number} addend The second number in an addition.
		     * @returns {number} Returns the total.
		     * @example
		     *
		     * _.add(6, 4);
		     * // => 10
		     */
		    var add = createMathOperation(function(augend, addend) {
		      return augend + addend;
		    }, 0);

		    /**
		     * Computes `number` rounded up to `precision`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.10.0
		     * @category Math
		     * @param {number} number The number to round up.
		     * @param {number} [precision=0] The precision to round up to.
		     * @returns {number} Returns the rounded up number.
		     * @example
		     *
		     * _.ceil(4.006);
		     * // => 5
		     *
		     * _.ceil(6.004, 2);
		     * // => 6.01
		     *
		     * _.ceil(6040, -2);
		     * // => 6100
		     */
		    var ceil = createRound('ceil');

		    /**
		     * Divide two numbers.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.7.0
		     * @category Math
		     * @param {number} dividend The first number in a division.
		     * @param {number} divisor The second number in a division.
		     * @returns {number} Returns the quotient.
		     * @example
		     *
		     * _.divide(6, 4);
		     * // => 1.5
		     */
		    var divide = createMathOperation(function(dividend, divisor) {
		      return dividend / divisor;
		    }, 1);

		    /**
		     * Computes `number` rounded down to `precision`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.10.0
		     * @category Math
		     * @param {number} number The number to round down.
		     * @param {number} [precision=0] The precision to round down to.
		     * @returns {number} Returns the rounded down number.
		     * @example
		     *
		     * _.floor(4.006);
		     * // => 4
		     *
		     * _.floor(0.046, 2);
		     * // => 0.04
		     *
		     * _.floor(4060, -2);
		     * // => 4000
		     */
		    var floor = createRound('floor');

		    /**
		     * Computes the maximum value of `array`. If `array` is empty or falsey,
		     * `undefined` is returned.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @returns {*} Returns the maximum value.
		     * @example
		     *
		     * _.max([4, 2, 8, 6]);
		     * // => 8
		     *
		     * _.max([]);
		     * // => undefined
		     */
		    function max(array) {
		      return (array && array.length)
		        ? baseExtremum(array, identity, baseGt)
		        : undefined$1;
		    }

		    /**
		     * This method is like `_.max` except that it accepts `iteratee` which is
		     * invoked for each element in `array` to generate the criterion by which
		     * the value is ranked. The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {*} Returns the maximum value.
		     * @example
		     *
		     * var objects = [{ 'n': 1 }, { 'n': 2 }];
		     *
		     * _.maxBy(objects, function(o) { return o.n; });
		     * // => { 'n': 2 }
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.maxBy(objects, 'n');
		     * // => { 'n': 2 }
		     */
		    function maxBy(array, iteratee) {
		      return (array && array.length)
		        ? baseExtremum(array, getIteratee(iteratee, 2), baseGt)
		        : undefined$1;
		    }

		    /**
		     * Computes the mean of the values in `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @returns {number} Returns the mean.
		     * @example
		     *
		     * _.mean([4, 2, 8, 6]);
		     * // => 5
		     */
		    function mean(array) {
		      return baseMean(array, identity);
		    }

		    /**
		     * This method is like `_.mean` except that it accepts `iteratee` which is
		     * invoked for each element in `array` to generate the value to be averaged.
		     * The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.7.0
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {number} Returns the mean.
		     * @example
		     *
		     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
		     *
		     * _.meanBy(objects, function(o) { return o.n; });
		     * // => 5
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.meanBy(objects, 'n');
		     * // => 5
		     */
		    function meanBy(array, iteratee) {
		      return baseMean(array, getIteratee(iteratee, 2));
		    }

		    /**
		     * Computes the minimum value of `array`. If `array` is empty or falsey,
		     * `undefined` is returned.
		     *
		     * @static
		     * @since 0.1.0
		     * @memberOf _
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @returns {*} Returns the minimum value.
		     * @example
		     *
		     * _.min([4, 2, 8, 6]);
		     * // => 2
		     *
		     * _.min([]);
		     * // => undefined
		     */
		    function min(array) {
		      return (array && array.length)
		        ? baseExtremum(array, identity, baseLt)
		        : undefined$1;
		    }

		    /**
		     * This method is like `_.min` except that it accepts `iteratee` which is
		     * invoked for each element in `array` to generate the criterion by which
		     * the value is ranked. The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {*} Returns the minimum value.
		     * @example
		     *
		     * var objects = [{ 'n': 1 }, { 'n': 2 }];
		     *
		     * _.minBy(objects, function(o) { return o.n; });
		     * // => { 'n': 1 }
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.minBy(objects, 'n');
		     * // => { 'n': 1 }
		     */
		    function minBy(array, iteratee) {
		      return (array && array.length)
		        ? baseExtremum(array, getIteratee(iteratee, 2), baseLt)
		        : undefined$1;
		    }

		    /**
		     * Multiply two numbers.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.7.0
		     * @category Math
		     * @param {number} multiplier The first number in a multiplication.
		     * @param {number} multiplicand The second number in a multiplication.
		     * @returns {number} Returns the product.
		     * @example
		     *
		     * _.multiply(6, 4);
		     * // => 24
		     */
		    var multiply = createMathOperation(function(multiplier, multiplicand) {
		      return multiplier * multiplicand;
		    }, 1);

		    /**
		     * Computes `number` rounded to `precision`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.10.0
		     * @category Math
		     * @param {number} number The number to round.
		     * @param {number} [precision=0] The precision to round to.
		     * @returns {number} Returns the rounded number.
		     * @example
		     *
		     * _.round(4.006);
		     * // => 4
		     *
		     * _.round(4.006, 2);
		     * // => 4.01
		     *
		     * _.round(4060, -2);
		     * // => 4100
		     */
		    var round = createRound('round');

		    /**
		     * Subtract two numbers.
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Math
		     * @param {number} minuend The first number in a subtraction.
		     * @param {number} subtrahend The second number in a subtraction.
		     * @returns {number} Returns the difference.
		     * @example
		     *
		     * _.subtract(6, 4);
		     * // => 2
		     */
		    var subtract = createMathOperation(function(minuend, subtrahend) {
		      return minuend - subtrahend;
		    }, 0);

		    /**
		     * Computes the sum of the values in `array`.
		     *
		     * @static
		     * @memberOf _
		     * @since 3.4.0
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @returns {number} Returns the sum.
		     * @example
		     *
		     * _.sum([4, 2, 8, 6]);
		     * // => 20
		     */
		    function sum(array) {
		      return (array && array.length)
		        ? baseSum(array, identity)
		        : 0;
		    }

		    /**
		     * This method is like `_.sum` except that it accepts `iteratee` which is
		     * invoked for each element in `array` to generate the value to be summed.
		     * The iteratee is invoked with one argument: (value).
		     *
		     * @static
		     * @memberOf _
		     * @since 4.0.0
		     * @category Math
		     * @param {Array} array The array to iterate over.
		     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
		     * @returns {number} Returns the sum.
		     * @example
		     *
		     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
		     *
		     * _.sumBy(objects, function(o) { return o.n; });
		     * // => 20
		     *
		     * // The `_.property` iteratee shorthand.
		     * _.sumBy(objects, 'n');
		     * // => 20
		     */
		    function sumBy(array, iteratee) {
		      return (array && array.length)
		        ? baseSum(array, getIteratee(iteratee, 2))
		        : 0;
		    }

		    /*------------------------------------------------------------------------*/

		    // Add methods that return wrapped values in chain sequences.
		    lodash.after = after;
		    lodash.ary = ary;
		    lodash.assign = assign;
		    lodash.assignIn = assignIn;
		    lodash.assignInWith = assignInWith;
		    lodash.assignWith = assignWith;
		    lodash.at = at;
		    lodash.before = before;
		    lodash.bind = bind;
		    lodash.bindAll = bindAll;
		    lodash.bindKey = bindKey;
		    lodash.castArray = castArray;
		    lodash.chain = chain;
		    lodash.chunk = chunk;
		    lodash.compact = compact;
		    lodash.concat = concat;
		    lodash.cond = cond;
		    lodash.conforms = conforms;
		    lodash.constant = constant;
		    lodash.countBy = countBy;
		    lodash.create = create;
		    lodash.curry = curry;
		    lodash.curryRight = curryRight;
		    lodash.debounce = debounce;
		    lodash.defaults = defaults;
		    lodash.defaultsDeep = defaultsDeep;
		    lodash.defer = defer;
		    lodash.delay = delay;
		    lodash.difference = difference;
		    lodash.differenceBy = differenceBy;
		    lodash.differenceWith = differenceWith;
		    lodash.drop = drop;
		    lodash.dropRight = dropRight;
		    lodash.dropRightWhile = dropRightWhile;
		    lodash.dropWhile = dropWhile;
		    lodash.fill = fill;
		    lodash.filter = filter;
		    lodash.flatMap = flatMap;
		    lodash.flatMapDeep = flatMapDeep;
		    lodash.flatMapDepth = flatMapDepth;
		    lodash.flatten = flatten;
		    lodash.flattenDeep = flattenDeep;
		    lodash.flattenDepth = flattenDepth;
		    lodash.flip = flip;
		    lodash.flow = flow;
		    lodash.flowRight = flowRight;
		    lodash.fromPairs = fromPairs;
		    lodash.functions = functions;
		    lodash.functionsIn = functionsIn;
		    lodash.groupBy = groupBy;
		    lodash.initial = initial;
		    lodash.intersection = intersection;
		    lodash.intersectionBy = intersectionBy;
		    lodash.intersectionWith = intersectionWith;
		    lodash.invert = invert;
		    lodash.invertBy = invertBy;
		    lodash.invokeMap = invokeMap;
		    lodash.iteratee = iteratee;
		    lodash.keyBy = keyBy;
		    lodash.keys = keys;
		    lodash.keysIn = keysIn;
		    lodash.map = map;
		    lodash.mapKeys = mapKeys;
		    lodash.mapValues = mapValues;
		    lodash.matches = matches;
		    lodash.matchesProperty = matchesProperty;
		    lodash.memoize = memoize;
		    lodash.merge = merge;
		    lodash.mergeWith = mergeWith;
		    lodash.method = method;
		    lodash.methodOf = methodOf;
		    lodash.mixin = mixin;
		    lodash.negate = negate;
		    lodash.nthArg = nthArg;
		    lodash.omit = omit;
		    lodash.omitBy = omitBy;
		    lodash.once = once;
		    lodash.orderBy = orderBy;
		    lodash.over = over;
		    lodash.overArgs = overArgs;
		    lodash.overEvery = overEvery;
		    lodash.overSome = overSome;
		    lodash.partial = partial;
		    lodash.partialRight = partialRight;
		    lodash.partition = partition;
		    lodash.pick = pick;
		    lodash.pickBy = pickBy;
		    lodash.property = property;
		    lodash.propertyOf = propertyOf;
		    lodash.pull = pull;
		    lodash.pullAll = pullAll;
		    lodash.pullAllBy = pullAllBy;
		    lodash.pullAllWith = pullAllWith;
		    lodash.pullAt = pullAt;
		    lodash.range = range;
		    lodash.rangeRight = rangeRight;
		    lodash.rearg = rearg;
		    lodash.reject = reject;
		    lodash.remove = remove;
		    lodash.rest = rest;
		    lodash.reverse = reverse;
		    lodash.sampleSize = sampleSize;
		    lodash.set = set;
		    lodash.setWith = setWith;
		    lodash.shuffle = shuffle;
		    lodash.slice = slice;
		    lodash.sortBy = sortBy;
		    lodash.sortedUniq = sortedUniq;
		    lodash.sortedUniqBy = sortedUniqBy;
		    lodash.split = split;
		    lodash.spread = spread;
		    lodash.tail = tail;
		    lodash.take = take;
		    lodash.takeRight = takeRight;
		    lodash.takeRightWhile = takeRightWhile;
		    lodash.takeWhile = takeWhile;
		    lodash.tap = tap;
		    lodash.throttle = throttle;
		    lodash.thru = thru;
		    lodash.toArray = toArray;
		    lodash.toPairs = toPairs;
		    lodash.toPairsIn = toPairsIn;
		    lodash.toPath = toPath;
		    lodash.toPlainObject = toPlainObject;
		    lodash.transform = transform;
		    lodash.unary = unary;
		    lodash.union = union;
		    lodash.unionBy = unionBy;
		    lodash.unionWith = unionWith;
		    lodash.uniq = uniq;
		    lodash.uniqBy = uniqBy;
		    lodash.uniqWith = uniqWith;
		    lodash.unset = unset;
		    lodash.unzip = unzip;
		    lodash.unzipWith = unzipWith;
		    lodash.update = update;
		    lodash.updateWith = updateWith;
		    lodash.values = values;
		    lodash.valuesIn = valuesIn;
		    lodash.without = without;
		    lodash.words = words;
		    lodash.wrap = wrap;
		    lodash.xor = xor;
		    lodash.xorBy = xorBy;
		    lodash.xorWith = xorWith;
		    lodash.zip = zip;
		    lodash.zipObject = zipObject;
		    lodash.zipObjectDeep = zipObjectDeep;
		    lodash.zipWith = zipWith;

		    // Add aliases.
		    lodash.entries = toPairs;
		    lodash.entriesIn = toPairsIn;
		    lodash.extend = assignIn;
		    lodash.extendWith = assignInWith;

		    // Add methods to `lodash.prototype`.
		    mixin(lodash, lodash);

		    /*------------------------------------------------------------------------*/

		    // Add methods that return unwrapped values in chain sequences.
		    lodash.add = add;
		    lodash.attempt = attempt;
		    lodash.camelCase = camelCase;
		    lodash.capitalize = capitalize;
		    lodash.ceil = ceil;
		    lodash.clamp = clamp;
		    lodash.clone = clone;
		    lodash.cloneDeep = cloneDeep;
		    lodash.cloneDeepWith = cloneDeepWith;
		    lodash.cloneWith = cloneWith;
		    lodash.conformsTo = conformsTo;
		    lodash.deburr = deburr;
		    lodash.defaultTo = defaultTo;
		    lodash.divide = divide;
		    lodash.endsWith = endsWith;
		    lodash.eq = eq;
		    lodash.escape = escape;
		    lodash.escapeRegExp = escapeRegExp;
		    lodash.every = every;
		    lodash.find = find;
		    lodash.findIndex = findIndex;
		    lodash.findKey = findKey;
		    lodash.findLast = findLast;
		    lodash.findLastIndex = findLastIndex;
		    lodash.findLastKey = findLastKey;
		    lodash.floor = floor;
		    lodash.forEach = forEach;
		    lodash.forEachRight = forEachRight;
		    lodash.forIn = forIn;
		    lodash.forInRight = forInRight;
		    lodash.forOwn = forOwn;
		    lodash.forOwnRight = forOwnRight;
		    lodash.get = get;
		    lodash.gt = gt;
		    lodash.gte = gte;
		    lodash.has = has;
		    lodash.hasIn = hasIn;
		    lodash.head = head;
		    lodash.identity = identity;
		    lodash.includes = includes;
		    lodash.indexOf = indexOf;
		    lodash.inRange = inRange;
		    lodash.invoke = invoke;
		    lodash.isArguments = isArguments;
		    lodash.isArray = isArray;
		    lodash.isArrayBuffer = isArrayBuffer;
		    lodash.isArrayLike = isArrayLike;
		    lodash.isArrayLikeObject = isArrayLikeObject;
		    lodash.isBoolean = isBoolean;
		    lodash.isBuffer = isBuffer;
		    lodash.isDate = isDate;
		    lodash.isElement = isElement;
		    lodash.isEmpty = isEmpty;
		    lodash.isEqual = isEqual;
		    lodash.isEqualWith = isEqualWith;
		    lodash.isError = isError;
		    lodash.isFinite = isFinite;
		    lodash.isFunction = isFunction;
		    lodash.isInteger = isInteger;
		    lodash.isLength = isLength;
		    lodash.isMap = isMap;
		    lodash.isMatch = isMatch;
		    lodash.isMatchWith = isMatchWith;
		    lodash.isNaN = isNaN;
		    lodash.isNative = isNative;
		    lodash.isNil = isNil;
		    lodash.isNull = isNull;
		    lodash.isNumber = isNumber;
		    lodash.isObject = isObject;
		    lodash.isObjectLike = isObjectLike;
		    lodash.isPlainObject = isPlainObject;
		    lodash.isRegExp = isRegExp;
		    lodash.isSafeInteger = isSafeInteger;
		    lodash.isSet = isSet;
		    lodash.isString = isString;
		    lodash.isSymbol = isSymbol;
		    lodash.isTypedArray = isTypedArray;
		    lodash.isUndefined = isUndefined;
		    lodash.isWeakMap = isWeakMap;
		    lodash.isWeakSet = isWeakSet;
		    lodash.join = join;
		    lodash.kebabCase = kebabCase;
		    lodash.last = last;
		    lodash.lastIndexOf = lastIndexOf;
		    lodash.lowerCase = lowerCase;
		    lodash.lowerFirst = lowerFirst;
		    lodash.lt = lt;
		    lodash.lte = lte;
		    lodash.max = max;
		    lodash.maxBy = maxBy;
		    lodash.mean = mean;
		    lodash.meanBy = meanBy;
		    lodash.min = min;
		    lodash.minBy = minBy;
		    lodash.stubArray = stubArray;
		    lodash.stubFalse = stubFalse;
		    lodash.stubObject = stubObject;
		    lodash.stubString = stubString;
		    lodash.stubTrue = stubTrue;
		    lodash.multiply = multiply;
		    lodash.nth = nth;
		    lodash.noConflict = noConflict;
		    lodash.noop = noop;
		    lodash.now = now;
		    lodash.pad = pad;
		    lodash.padEnd = padEnd;
		    lodash.padStart = padStart;
		    lodash.parseInt = parseInt;
		    lodash.random = random;
		    lodash.reduce = reduce;
		    lodash.reduceRight = reduceRight;
		    lodash.repeat = repeat;
		    lodash.replace = replace;
		    lodash.result = result;
		    lodash.round = round;
		    lodash.runInContext = runInContext;
		    lodash.sample = sample;
		    lodash.size = size;
		    lodash.snakeCase = snakeCase;
		    lodash.some = some;
		    lodash.sortedIndex = sortedIndex;
		    lodash.sortedIndexBy = sortedIndexBy;
		    lodash.sortedIndexOf = sortedIndexOf;
		    lodash.sortedLastIndex = sortedLastIndex;
		    lodash.sortedLastIndexBy = sortedLastIndexBy;
		    lodash.sortedLastIndexOf = sortedLastIndexOf;
		    lodash.startCase = startCase;
		    lodash.startsWith = startsWith;
		    lodash.subtract = subtract;
		    lodash.sum = sum;
		    lodash.sumBy = sumBy;
		    lodash.template = template;
		    lodash.times = times;
		    lodash.toFinite = toFinite;
		    lodash.toInteger = toInteger;
		    lodash.toLength = toLength;
		    lodash.toLower = toLower;
		    lodash.toNumber = toNumber;
		    lodash.toSafeInteger = toSafeInteger;
		    lodash.toString = toString;
		    lodash.toUpper = toUpper;
		    lodash.trim = trim;
		    lodash.trimEnd = trimEnd;
		    lodash.trimStart = trimStart;
		    lodash.truncate = truncate;
		    lodash.unescape = unescape;
		    lodash.uniqueId = uniqueId;
		    lodash.upperCase = upperCase;
		    lodash.upperFirst = upperFirst;

		    // Add aliases.
		    lodash.each = forEach;
		    lodash.eachRight = forEachRight;
		    lodash.first = head;

		    mixin(lodash, (function() {
		      var source = {};
		      baseForOwn(lodash, function(func, methodName) {
		        if (!hasOwnProperty.call(lodash.prototype, methodName)) {
		          source[methodName] = func;
		        }
		      });
		      return source;
		    }()), { 'chain': false });

		    /*------------------------------------------------------------------------*/

		    /**
		     * The semantic version number.
		     *
		     * @static
		     * @memberOf _
		     * @type {string}
		     */
		    lodash.VERSION = VERSION;

		    // Assign default placeholders.
		    arrayEach(['bind', 'bindKey', 'curry', 'curryRight', 'partial', 'partialRight'], function(methodName) {
		      lodash[methodName].placeholder = lodash;
		    });

		    // Add `LazyWrapper` methods for `_.drop` and `_.take` variants.
		    arrayEach(['drop', 'take'], function(methodName, index) {
		      LazyWrapper.prototype[methodName] = function(n) {
		        n = n === undefined$1 ? 1 : nativeMax(toInteger(n), 0);

		        var result = (this.__filtered__ && !index)
		          ? new LazyWrapper(this)
		          : this.clone();

		        if (result.__filtered__) {
		          result.__takeCount__ = nativeMin(n, result.__takeCount__);
		        } else {
		          result.__views__.push({
		            'size': nativeMin(n, MAX_ARRAY_LENGTH),
		            'type': methodName + (result.__dir__ < 0 ? 'Right' : '')
		          });
		        }
		        return result;
		      };

		      LazyWrapper.prototype[methodName + 'Right'] = function(n) {
		        return this.reverse()[methodName](n).reverse();
		      };
		    });

		    // Add `LazyWrapper` methods that accept an `iteratee` value.
		    arrayEach(['filter', 'map', 'takeWhile'], function(methodName, index) {
		      var type = index + 1,
		          isFilter = type == LAZY_FILTER_FLAG || type == LAZY_WHILE_FLAG;

		      LazyWrapper.prototype[methodName] = function(iteratee) {
		        var result = this.clone();
		        result.__iteratees__.push({
		          'iteratee': getIteratee(iteratee, 3),
		          'type': type
		        });
		        result.__filtered__ = result.__filtered__ || isFilter;
		        return result;
		      };
		    });

		    // Add `LazyWrapper` methods for `_.head` and `_.last`.
		    arrayEach(['head', 'last'], function(methodName, index) {
		      var takeName = 'take' + (index ? 'Right' : '');

		      LazyWrapper.prototype[methodName] = function() {
		        return this[takeName](1).value()[0];
		      };
		    });

		    // Add `LazyWrapper` methods for `_.initial` and `_.tail`.
		    arrayEach(['initial', 'tail'], function(methodName, index) {
		      var dropName = 'drop' + (index ? '' : 'Right');

		      LazyWrapper.prototype[methodName] = function() {
		        return this.__filtered__ ? new LazyWrapper(this) : this[dropName](1);
		      };
		    });

		    LazyWrapper.prototype.compact = function() {
		      return this.filter(identity);
		    };

		    LazyWrapper.prototype.find = function(predicate) {
		      return this.filter(predicate).head();
		    };

		    LazyWrapper.prototype.findLast = function(predicate) {
		      return this.reverse().find(predicate);
		    };

		    LazyWrapper.prototype.invokeMap = baseRest(function(path, args) {
		      if (typeof path == 'function') {
		        return new LazyWrapper(this);
		      }
		      return this.map(function(value) {
		        return baseInvoke(value, path, args);
		      });
		    });

		    LazyWrapper.prototype.reject = function(predicate) {
		      return this.filter(negate(getIteratee(predicate)));
		    };

		    LazyWrapper.prototype.slice = function(start, end) {
		      start = toInteger(start);

		      var result = this;
		      if (result.__filtered__ && (start > 0 || end < 0)) {
		        return new LazyWrapper(result);
		      }
		      if (start < 0) {
		        result = result.takeRight(-start);
		      } else if (start) {
		        result = result.drop(start);
		      }
		      if (end !== undefined$1) {
		        end = toInteger(end);
		        result = end < 0 ? result.dropRight(-end) : result.take(end - start);
		      }
		      return result;
		    };

		    LazyWrapper.prototype.takeRightWhile = function(predicate) {
		      return this.reverse().takeWhile(predicate).reverse();
		    };

		    LazyWrapper.prototype.toArray = function() {
		      return this.take(MAX_ARRAY_LENGTH);
		    };

		    // Add `LazyWrapper` methods to `lodash.prototype`.
		    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
		      var checkIteratee = /^(?:filter|find|map|reject)|While$/.test(methodName),
		          isTaker = /^(?:head|last)$/.test(methodName),
		          lodashFunc = lodash[isTaker ? ('take' + (methodName == 'last' ? 'Right' : '')) : methodName],
		          retUnwrapped = isTaker || /^find/.test(methodName);

		      if (!lodashFunc) {
		        return;
		      }
		      lodash.prototype[methodName] = function() {
		        var value = this.__wrapped__,
		            args = isTaker ? [1] : arguments,
		            isLazy = value instanceof LazyWrapper,
		            iteratee = args[0],
		            useLazy = isLazy || isArray(value);

		        var interceptor = function(value) {
		          var result = lodashFunc.apply(lodash, arrayPush([value], args));
		          return (isTaker && chainAll) ? result[0] : result;
		        };

		        if (useLazy && checkIteratee && typeof iteratee == 'function' && iteratee.length != 1) {
		          // Avoid lazy use if the iteratee has a "length" value other than `1`.
		          isLazy = useLazy = false;
		        }
		        var chainAll = this.__chain__,
		            isHybrid = !!this.__actions__.length,
		            isUnwrapped = retUnwrapped && !chainAll,
		            onlyLazy = isLazy && !isHybrid;

		        if (!retUnwrapped && useLazy) {
		          value = onlyLazy ? value : new LazyWrapper(this);
		          var result = func.apply(value, args);
		          result.__actions__.push({ 'func': thru, 'args': [interceptor], 'thisArg': undefined$1 });
		          return new LodashWrapper(result, chainAll);
		        }
		        if (isUnwrapped && onlyLazy) {
		          return func.apply(this, args);
		        }
		        result = this.thru(interceptor);
		        return isUnwrapped ? (isTaker ? result.value()[0] : result.value()) : result;
		      };
		    });

		    // Add `Array` methods to `lodash.prototype`.
		    arrayEach(['pop', 'push', 'shift', 'sort', 'splice', 'unshift'], function(methodName) {
		      var func = arrayProto[methodName],
		          chainName = /^(?:push|sort|unshift)$/.test(methodName) ? 'tap' : 'thru',
		          retUnwrapped = /^(?:pop|shift)$/.test(methodName);

		      lodash.prototype[methodName] = function() {
		        var args = arguments;
		        if (retUnwrapped && !this.__chain__) {
		          var value = this.value();
		          return func.apply(isArray(value) ? value : [], args);
		        }
		        return this[chainName](function(value) {
		          return func.apply(isArray(value) ? value : [], args);
		        });
		      };
		    });

		    // Map minified method names to their real names.
		    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
		      var lodashFunc = lodash[methodName];
		      if (lodashFunc) {
		        var key = lodashFunc.name + '';
		        if (!hasOwnProperty.call(realNames, key)) {
		          realNames[key] = [];
		        }
		        realNames[key].push({ 'name': methodName, 'func': lodashFunc });
		      }
		    });

		    realNames[createHybrid(undefined$1, WRAP_BIND_KEY_FLAG).name] = [{
		      'name': 'wrapper',
		      'func': undefined$1
		    }];

		    // Add methods to `LazyWrapper`.
		    LazyWrapper.prototype.clone = lazyClone;
		    LazyWrapper.prototype.reverse = lazyReverse;
		    LazyWrapper.prototype.value = lazyValue;

		    // Add chain sequence methods to the `lodash` wrapper.
		    lodash.prototype.at = wrapperAt;
		    lodash.prototype.chain = wrapperChain;
		    lodash.prototype.commit = wrapperCommit;
		    lodash.prototype.next = wrapperNext;
		    lodash.prototype.plant = wrapperPlant;
		    lodash.prototype.reverse = wrapperReverse;
		    lodash.prototype.toJSON = lodash.prototype.valueOf = lodash.prototype.value = wrapperValue;

		    // Add lazy aliases.
		    lodash.prototype.first = lodash.prototype.head;

		    if (symIterator) {
		      lodash.prototype[symIterator] = wrapperToIterator;
		    }
		    return lodash;
		  });

		  /*--------------------------------------------------------------------------*/

		  // Export lodash.
		  var _ = runInContext();

		  // Some AMD build optimizers, like r.js, check for condition patterns like:
		  if (freeModule) {
		    // Export for Node.js.
		    (freeModule.exports = _)._ = _;
		    // Export for CommonJS support.
		    freeExports._ = _;
		  }
		  else {
		    // Export to the global object.
		    root._ = _;
		  }
		}.call(lodash)); 
	} (lodash$1, lodash$1.exports));
	return lodash$1.exports;
}

var hasRequiredObjectUtils;

function requireObjectUtils () {
	if (hasRequiredObjectUtils) return objectUtils;
	hasRequiredObjectUtils = 1;
	Object.defineProperty(objectUtils, "__esModule", { value: true });
	objectUtils.filterObjectUndefinedValues = filterObjectUndefinedValues;
	objectUtils.sortObjectKeysRecursively = sortObjectKeysRecursively;
	const lodash_1 = requireLodash();
	function filterObjectUndefinedValues(obj) {
	    return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
	}
	function sortObjectKeysRecursively(obj) {
	    if (obj === null || obj === undefined) {
	        return obj;
	    }
	    if (Array.isArray(obj)) {
	        return obj.map((item) => sortObjectKeysRecursively(item));
	    }
	    if ((0, lodash_1.isPlainObject)(obj)) {
	        const sortedKeys = Object.keys(obj).sort();
	        const sortedObj = {};
	        for (const key of sortedKeys) {
	            sortedObj[key] = sortObjectKeysRecursively(obj[key]);
	        }
	        return sortedObj;
	    }
	    return obj;
	}
	
	return objectUtils;
}

var parsedCallStack = {};

var hasRequiredParsedCallStack;

function requireParsedCallStack () {
	if (hasRequiredParsedCallStack) return parsedCallStack;
	hasRequiredParsedCallStack = 1;
	Object.defineProperty(parsedCallStack, "__esModule", { value: true });
	parsedCallStack.ParsedCallStack = parsedCallStack.ParsedCallStackLine = void 0;
	parsedCallStack.parseCallStack = parseCallStack;
	class ParsedCallStackLine {
	    constructor(raw, methodName, options = {}) {
	        this.raw = raw;
	        this.methodName = methodName;
	        this.lineNumber = options.lineNumber;
	        this.arguments = options.arguments;
	        this.file = options.file;
	        this.column = options.column;
	        this.isAnonymous = options.isAnonymous ?? false;
	    }
	    get hasFileLocation() {
	        return Boolean(this.file);
	    }
	    async inNodeModulesOrSystem(fs, path) {
	        if (!this.file) {
	            return false;
	        }
	        if (this.file.startsWith('node:')) {
	            return true;
	        }
	        if (this._inNodeModules === undefined) {
	            let realPath;
	            try {
	                realPath = await fs.realpath(this.file);
	            }
	            catch {
	                realPath = this.file;
	            }
	            const pathSegments = realPath.split(path.sep);
	            this._inNodeModules = pathSegments.includes('node_modules');
	        }
	        return this._inNodeModules;
	    }
	    static fromStackLine(line) {
	        const cleanLine = line.replace(/^\s*at\s+/, '');
	        const isAnonymous = cleanLine.includes('<anonymous>');
	        const filePathMatch = cleanLine.match(/^(.+?)\s+\((.+?):(\d+):(\d+)\)$/);
	        if (filePathMatch) {
	            const [, methodPart, file, lineStr, columnStr] = filePathMatch;
	            return new ParsedCallStackLine(line, methodPart.trim(), {
	                file,
	                lineNumber: parseInt(lineStr, 10),
	                column: parseInt(columnStr, 10),
	                isAnonymous,
	            });
	        }
	        const directFileMatch = cleanLine.match(/^(.+?):(\d+):(\d+)$/);
	        if (directFileMatch) {
	            const [, file, lineStr, columnStr] = directFileMatch;
	            return new ParsedCallStackLine(line, '<unknown>', {
	                file,
	                lineNumber: parseInt(lineStr, 10),
	                column: parseInt(columnStr, 10),
	                isAnonymous,
	            });
	        }
	        const anonymousMatch = cleanLine.match(/^(.+?)\s+\(<anonymous>\)$/);
	        if (anonymousMatch) {
	            const [, methodName] = anonymousMatch;
	            return new ParsedCallStackLine(line, methodName.trim(), {
	                isAnonymous: true,
	            });
	        }
	        const methodMatch = cleanLine.match(/^(.+?)(\(.*\))?$/);
	        if (methodMatch) {
	            const [, methodPart, argsPart] = methodMatch;
	            return new ParsedCallStackLine(line, methodPart.trim(), {
	                arguments: argsPart?.trim(),
	                isAnonymous,
	            });
	        }
	        return new ParsedCallStackLine(line, cleanLine, {
	            isAnonymous,
	        });
	    }
	    toString() {
	        return this.raw;
	    }
	}
	parsedCallStack.ParsedCallStackLine = ParsedCallStackLine;
	class ParsedCallStack {
	    constructor(stackLines) {
	        this.stackLines = stackLines;
	    }
	    async hasLineNotInNodeModules(fs, path, { ignorePaths = [], skipLines = 0 } = {}) {
	        let lineNo = 0;
	        for (const line of this.stackLines) {
	            if (lineNo++ < skipLines) {
	                continue;
	            }
	            if (!line.hasFileLocation) {
	                continue;
	            }
	            if (line.file && ignorePaths.some((p) => line.file?.includes(p))) {
	                continue;
	            }
	            if (!(await line.inNodeModulesOrSystem(fs, path))) {
	                return true;
	            }
	        }
	        return false;
	    }
	    async getLinesNotInNodeModulesOrSystem(fs, path) {
	        return Promise.all(this.stackLines.map((line) => line.inNodeModulesOrSystem(fs, path))).then((inNodeModules) => this.stackLines.filter((line, index) => !line.hasFileLocation || !inNodeModules[index]));
	    }
	    getLines() {
	        return this.stackLines;
	    }
	    static fromStackTrace(stackTrace) {
	        const lines = stackTrace.split('\n');
	        const stackLines = [];
	        for (const line of lines) {
	            const trimmedLine = line.trim();
	            if (!trimmedLine || !trimmedLine.startsWith('at ')) {
	                continue;
	            }
	            const parsedLine = ParsedCallStackLine.fromStackLine(trimmedLine);
	            if (parsedLine) {
	                stackLines.push(parsedLine);
	            }
	        }
	        return new ParsedCallStack(stackLines);
	    }
	}
	parsedCallStack.ParsedCallStack = ParsedCallStack;
	function parseCallStack(stackTrace) {
	    return ParsedCallStack.fromStackTrace(stackTrace);
	}
	
	return parsedCallStack;
}

var promiseAllSettledWithAbort = {};

var summarizedAggregateError = {};

var hasRequiredSummarizedAggregateError;

function requireSummarizedAggregateError () {
	if (hasRequiredSummarizedAggregateError) return summarizedAggregateError;
	hasRequiredSummarizedAggregateError = 1;
	Object.defineProperty(summarizedAggregateError, "__esModule", { value: true });
	summarizedAggregateError.SummarizedAggregateError = void 0;
	class SummarizedAggregateError extends AggregateError {
	    constructor(errors, message) {
	        super(errors, message ??
	            `${errors.length} Errors:\n${errors
	                .map((e) => (typeof e === 'object' && e !== null && 'message' in e ? e.message : e))
	                .filter(Boolean)
	                .join('\n')}`);
	        this.errors = errors;
	        Object.setPrototypeOf(this, SummarizedAggregateError.prototype);
	    }
	}
	summarizedAggregateError.SummarizedAggregateError = SummarizedAggregateError;
	
	return summarizedAggregateError;
}

var hasRequiredPromiseAllSettledWithAbort;

function requirePromiseAllSettledWithAbort () {
	if (hasRequiredPromiseAllSettledWithAbort) return promiseAllSettledWithAbort;
	hasRequiredPromiseAllSettledWithAbort = 1;
	Object.defineProperty(promiseAllSettledWithAbort, "__esModule", { value: true });
	promiseAllSettledWithAbort.promiseAllSettledWithAbort = promiseAllSettledWithAbort$1;
	const summarized_aggregate_error_1 = requireSummarizedAggregateError();
	async function promiseAllSettledWithAbort$1(promises, abortController) {
	    const errors = [];
	    const results = await Promise.allSettled(promises.map(async (p) => {
	        try {
	            return await p;
	        }
	        catch (e) {
	            abortController.abort();
	            throw e;
	        }
	    }));
	    const resultValues = [];
	    for (const result of results) {
	        if (result.status === 'rejected') {
	            errors.push(result.reason);
	            resultValues.push(undefined);
	        }
	        else if (result.status === 'fulfilled') {
	            resultValues.push(result.value);
	        }
	    }
	    if (errors.length > 0) {
	        if (errors.length === 1) {
	            throw errors[0];
	        }
	        throw new summarized_aggregate_error_1.SummarizedAggregateError(errors);
	    }
	    return resultValues;
	}
	
	return promiseAllSettledWithAbort;
}

var promiseAllSettled = {};

var hasRequiredPromiseAllSettled;

function requirePromiseAllSettled () {
	if (hasRequiredPromiseAllSettled) return promiseAllSettled;
	hasRequiredPromiseAllSettled = 1;
	Object.defineProperty(promiseAllSettled, "__esModule", { value: true });
	promiseAllSettled.promiseAllSettled = promiseAllSettled$1;
	const summarized_aggregate_error_1 = requireSummarizedAggregateError();
	async function promiseAllSettled$1(promises) {
	    const errors = [];
	    const results = await Promise.allSettled(promises);
	    const resultValues = [];
	    for (const result of results) {
	        if (result.status === 'rejected') {
	            errors.push(result.reason);
	        }
	        else if (result.status === 'fulfilled') {
	            resultValues.push(result.value);
	        }
	    }
	    if (errors.length > 0) {
	        if (errors.length === 1) {
	            throw errors[0];
	        }
	        throw new summarized_aggregate_error_1.SummarizedAggregateError(errors);
	    }
	    return resultValues;
	}
	
	return promiseAllSettled;
}

var RecordTypeFromMap = {};

var hasRequiredRecordTypeFromMap;

function requireRecordTypeFromMap () {
	if (hasRequiredRecordTypeFromMap) return RecordTypeFromMap;
	hasRequiredRecordTypeFromMap = 1;
	Object.defineProperty(RecordTypeFromMap, "__esModule", { value: true });
	
	return RecordTypeFromMap;
}

var requireFields = {};

var hasRequiredRequireFields;

function requireRequireFields () {
	if (hasRequiredRequireFields) return requireFields;
	hasRequiredRequireFields = 1;
	Object.defineProperty(requireFields, "__esModule", { value: true });
	requireFields.requireFields = requireFields$1;
	function requireFields$1(obj, keys) {
	    const result = {};
	    for (const key of keys) {
	        const value = obj[key];
	        if (value === null) {
	            throw new Error(`Missing required field: ${String(key)}`);
	        }
	        result[key] = value;
	    }
	    return result;
	}
	
	return requireFields;
}

var Sleep = {};

var hasRequiredSleep;

function requireSleep () {
	if (hasRequiredSleep) return Sleep;
	hasRequiredSleep = 1;
	Object.defineProperty(Sleep, "__esModule", { value: true });
	Sleep.sleep = sleep;
	function sleep(waitMs, abortSignal) {
	    if (waitMs <= 0 || abortSignal?.aborted) {
	        return Promise.resolve();
	    }
	    return new Promise((resolve) => {
	        abortSignal?.addEventListener('abort', onAbort);
	        const timeout = setTimeout(() => {
	            resolve();
	            abortSignal?.removeEventListener('abort', onAbort);
	        }, waitMs);
	        function onAbort() {
	            clearTimeout(timeout);
	            resolve();
	        }
	    });
	}
	
	return Sleep;
}

var StaticImplements = {};

var hasRequiredStaticImplements;

function requireStaticImplements () {
	if (hasRequiredStaticImplements) return StaticImplements;
	hasRequiredStaticImplements = 1;
	Object.defineProperty(StaticImplements, "__esModule", { value: true });
	StaticImplements.StaticImplements = StaticImplements$1;
	function StaticImplements$1() {
	    return (constructor) => {
	    };
	}
	
	return StaticImplements;
}

var StringUtils = {};

var hasRequiredStringUtils;

function requireStringUtils () {
	if (hasRequiredStringUtils) return StringUtils;
	hasRequiredStringUtils = 1;
	Object.defineProperty(StringUtils, "__esModule", { value: true });
	StringUtils.isNullOrWhitespace = isNullOrWhitespace;
	StringUtils.randomAlphaStr = randomAlphaStr;
	StringUtils.urlStrWithoutTrailingSlash = urlStrWithoutTrailingSlash;
	StringUtils.commonPrefix = commonPrefix;
	StringUtils.getOperandsFromExpression = getOperandsFromExpression;
	function isNullOrWhitespace(str) {
	    return str === null || str === undefined || str.match(/^ *$/) !== null;
	}
	const DefaultAlphaNumericDictionary = '0123456789abcdefghijklmnopqrstuvwxyz';
	function randomAlphaStr(length, dictionary = DefaultAlphaNumericDictionary) {
	    if (length <= 0 || !Number.isInteger(length)) {
	        throw new Error('String generator must receive positive integer value: ' + length);
	    }
	    if (dictionary.length === 0) {
	        throw new Error('String generator must receive non-empty dictionary');
	    }
	    return new Array(length)
	        .fill(0, 0, length)
	        .map(() => dictionary[Math.floor(Math.random() * dictionary.length)])
	        .join('');
	}
	function urlStrWithoutTrailingSlash(urlStr) {
	    while (urlStr.endsWith('/')) {
	        urlStr = urlStr.substr(0, urlStr.length - 1);
	    }
	    return urlStr;
	}
	function commonPrefix(str1, str2) {
	    const minLength = Math.min(str1.length, str2.length);
	    for (let i = 0; i < minLength; i++) {
	        if (str1[i] !== str2[i]) {
	            return str1.substring(0, i);
	        }
	    }
	    return str1;
	}
	function getOperandsFromExpression(expression) {
	    const scientificPlaceholders = [];
	    let protectedExpression = expression;
	    protectedExpression = protectedExpression.replace(/[eE][+-]\d+/g, (match) => {
	        const placeholder = `__SCI_${scientificPlaceholders.length}__`;
	        scientificPlaceholders.push({ placeholder, original: match });
	        return placeholder;
	    });
	    const operatorRegex = /[*\-+/()^]/g;
	    const operands = protectedExpression
	        .split(operatorRegex)
	        .map((part) => part.trim())
	        .filter((part) => part !== '');
	    return operands.map((operand) => {
	        let restored = operand;
	        scientificPlaceholders.forEach(({ placeholder, original }) => {
	            restored = restored.replace(placeholder, original);
	        });
	        return restored;
	    });
	}
	
	return StringUtils;
}

var ThunkMap = {};

var hasRequiredThunkMap;

function requireThunkMap () {
	if (hasRequiredThunkMap) return ThunkMap;
	hasRequiredThunkMap = 1;
	Object.defineProperty(ThunkMap, "__esModule", { value: true });
	ThunkMap.resolveThunkMap = resolveThunkMap;
	ThunkMap.resolveValueFromThunkMap = resolveValueFromThunkMap;
	ThunkMap.resolveValueFromThunk = resolveValueFromThunk;
	function resolveThunkMap(map) {
	    return Object.fromEntries(Object.entries(map).map(([key, value]) => [key, typeof value === 'function' ? value() : value]));
	}
	function resolveValueFromThunkMap(map, key, throwIfUndefined = false) {
	    const value = resolveValueFromThunk(map[key]);
	    if (throwIfUndefined && value === undefined) {
	        throw new Error(`Value for key ${String(key)} is undefined`);
	    }
	    return value;
	}
	function resolveValueFromThunk(value) {
	    return typeof value === 'function' ? value() : value;
	}
	
	return ThunkMap;
}

var toArray = {};

var hasRequiredToArray;

function requireToArray () {
	if (hasRequiredToArray) return toArray;
	hasRequiredToArray = 1;
	Object.defineProperty(toArray, "__esModule", { value: true });
	toArray.toArray = toArray$1;
	function toArray$1(obj) {
	    if (obj === null || obj === undefined) {
	        return [];
	    }
	    return Array.isArray(obj) ? obj : [obj];
	}
	
	return toArray;
}

var TypeUtils = {};

var hasRequiredTypeUtils;

function requireTypeUtils () {
	if (hasRequiredTypeUtils) return TypeUtils;
	hasRequiredTypeUtils = 1;
	Object.defineProperty(TypeUtils, "__esModule", { value: true });
	
	return TypeUtils;
}

var Utils$4 = {};

var hasRequiredUtils$5;

function requireUtils$5 () {
	if (hasRequiredUtils$5) return Utils$4;
	hasRequiredUtils$5 = 1;
	Object.defineProperty(Utils$4, "__esModule", { value: true });
	Utils$4.XYMap = void 0;
	Utils$4.scaleRect = scaleRect;
	Utils$4.constrainRect = constrainRect;
	Utils$4.normalizeRect = normalizeRect;
	Utils$4.constrainValue = constrainValue;
	Utils$4.valueBetween = valueBetween;
	Utils$4.max = max;
	Utils$4.maxA = maxA;
	Utils$4.min = min;
	Utils$4.constrainPositive = constrainPositive;
	Utils$4.assertInteger = assertInteger;
	Utils$4.booleanXor = booleanXor;
	Utils$4.combineMirrors = combineMirrors;
	Utils$4.distanceBetweenPoints = distanceBetweenPoints;
	Utils$4.distanceBetweenIPoints = distanceBetweenIPoints;
	function scaleRect(rect, scale) {
	    return {
	        x1: rect.x1 * scale,
	        x2: rect.x2 * scale,
	        y1: rect.y1 * scale,
	        y2: rect.y2 * scale,
	    };
	}
	function constrainRect(rect, boundingRect) {
	    rect = normalizeRect(rect);
	    boundingRect = normalizeRect(boundingRect);
	    return {
	        x1: constrainValue(boundingRect.x1, rect.x1, boundingRect.x2),
	        x2: constrainValue(boundingRect.x1, rect.x2, boundingRect.x2),
	        y1: constrainValue(boundingRect.y1, rect.y1, boundingRect.y2),
	        y2: constrainValue(boundingRect.y1, rect.y2, boundingRect.y2),
	    };
	}
	function normalizeRect(rect) {
	    return {
	        x1: min(rect.x1, rect.x2),
	        x2: max(rect.x1, rect.x2),
	        y1: min(rect.y1, rect.y2),
	        y2: max(rect.y1, rect.y2),
	    };
	}
	function constrainValue(min, value, max) {
	    if (min > max) {
	        const tmp = min;
	        min = max;
	        max = tmp;
	    }
	    if (value < min) {
	        return min;
	    }
	    if (value > max) {
	        return max;
	    }
	    return value;
	}
	function valueBetween(min, value, max, numEpsilons = 2) {
	    if (min > max) {
	        const tmp = min;
	        min = max;
	        max = tmp;
	    }
	    return !(value < min + numEpsilons * Number.EPSILON || value > max + numEpsilons * Number.EPSILON);
	}
	function max(a, b) {
	    if (typeof a !== 'number') {
	        if (typeof b === 'number') {
	            return b;
	        }
	        return Number.NaN;
	    }
	    if (typeof b !== 'number') {
	        return a;
	    }
	    if (a > b) {
	        return a;
	    }
	    return b;
	}
	function maxA(...a) {
	    if (a.length === 0) {
	        return Number.NaN;
	    }
	    let max = a[0];
	    for (let i = 1; i < a.length; ++i) {
	        if (a[i] > max) {
	            max = a[i];
	        }
	    }
	    return max;
	}
	function min(a, b) {
	    if (typeof a !== 'number') {
	        if (typeof b === 'number') {
	            return b;
	        }
	        return Number.NaN;
	    }
	    if (typeof b !== 'number') {
	        return a;
	    }
	    if (a < b) {
	        return a;
	    }
	    return b;
	}
	function constrainPositive(num) {
	    if (typeof num !== 'number') {
	        return 0;
	    }
	    if (num < 0) {
	        return 0;
	    }
	    return num;
	}
	function assertInteger(maybeNum) {
	    if (typeof maybeNum === 'string') {
	        return parseInt(maybeNum);
	    }
	    return maybeNum;
	}
	function booleanXor(a, b) {
	    return (a || b) && !(a && b);
	}
	function combineMirrors(a, b) {
	    return [booleanXor(Boolean(a && a[0]), Boolean(b && b[0])), booleanXor(Boolean(a && a[1]), Boolean(b && b[1]))];
	}
	function distanceBetweenPoints(a, b) {
	    return Math.sqrt(Math.pow(b[0] - a[0], 2) + Math.pow(b[1] - a[1], 2));
	}
	function distanceBetweenIPoints(a, b) {
	    return Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
	}
	class XYMap {
	    constructor() {
	        this.points = new Map();
	    }
	    set(x, y, val) {
	        if (!this.points.has(x)) {
	            this.points.set(x, new Map([[y, val]]));
	            return;
	        }
	        const xMap = this.points.get(x);
	        xMap.set(y, val);
	    }
	    has(x, y) {
	        return Boolean(this.points.get(x)?.has(y));
	    }
	    get(x, y) {
	        return this.points.get(x)?.get(y);
	    }
	    *values() {
	        for (const yMap of this.points.values()) {
	            for (const val of yMap.values()) {
	                yield val;
	            }
	        }
	    }
	    *entries() {
	        for (const [x, yMap] of this.points.entries()) {
	            for (const [y, val] of yMap.entries()) {
	                yield [x, y, val];
	            }
	        }
	    }
	}
	Utils$4.XYMap = XYMap;
	
	return Utils$4;
}

var ValueValidation = {};

var hasRequiredValueValidation;

function requireValueValidation () {
	if (hasRequiredValueValidation) return ValueValidation;
	hasRequiredValueValidation = 1;
	Object.defineProperty(ValueValidation, "__esModule", { value: true });
	ValueValidation.isEmptyValue = isEmptyValue;
	function isEmptyValue(value) {
	    if (value !== null && typeof value === 'object' && 'value' in value) {
	        return isEmptyValue(value.value);
	    }
	    return (value === undefined ||
	        value === null ||
	        (typeof value === 'object' && Object.keys(value).length === 0) ||
	        (typeof value === 'string' && value.trim() === ''));
	}
	
	return ValueValidation;
}

var zodOutputType = {};

var hasRequiredZodOutputType;

function requireZodOutputType () {
	if (hasRequiredZodOutputType) return zodOutputType;
	hasRequiredZodOutputType = 1;
	Object.defineProperty(zodOutputType, "__esModule", { value: true });
	
	return zodOutputType;
}

var zodPartial = {};

var zod = {};

var external = {};

var core$1 = {};

var core = {};

var util$1 = {};

var hasRequiredUtil$2;

function requireUtil$2 () {
	if (hasRequiredUtil$2) return util$1;
	hasRequiredUtil$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.CONSTANT_CATCH = exports.Class = exports.BIGINT_FORMAT_RANGES = exports.NUMBER_FORMAT_RANGES = exports.primitiveTypes = exports.propertyKeyTypes = exports.getParsedType = exports.allowsEval = exports.captureStackTrace = void 0;
		exports.assertEqual = assertEqual;
		exports.assertNotEqual = assertNotEqual;
		exports.toZod = toZod;
		exports.assertIs = assertIs;
		exports.assertNever = assertNever;
		exports.assert = assert;
		exports.getEnumValues = getEnumValues;
		exports.joinValues = joinValues;
		exports.jsonStringifyReplacer = jsonStringifyReplacer;
		exports.cached = cached;
		exports.nullish = nullish;
		exports.cleanRegex = cleanRegex;
		exports.floatSafeRemainder = floatSafeRemainder;
		exports.defineLazy = defineLazy;
		exports.objectClone = objectClone;
		exports.assignProp = assignProp;
		exports.rawShape = rawShape;
		exports.mergeDefs = mergeDefs;
		exports.cloneDef = cloneDef;
		exports.getElementAtPath = getElementAtPath;
		exports.promiseAllObject = promiseAllObject;
		exports.randomString = randomString;
		exports.esc = esc;
		exports.slugify = slugify;
		exports.isObject = isObject;
		exports.isPlainObject = isPlainObject;
		exports.shallowClone = shallowClone;
		exports.numKeys = numKeys;
		exports.escapeRegex = escapeRegex;
		exports.clone = clone;
		exports.normalizeParams = normalizeParams;
		exports.createTransparentProxy = createTransparentProxy;
		exports.stringifyPrimitive = stringifyPrimitive;
		exports.optionalKeys = optionalKeys;
		exports.pick = pick;
		exports.omit = omit;
		exports.extend = extend;
		exports.safeExtend = safeExtend;
		exports.merge = merge;
		exports.partial = partial;
		exports.required = required;
		exports.aborted = aborted;
		exports.explicitlyAborted = explicitlyAborted;
		exports.prefixIssues = prefixIssues;
		exports.unwrapMessage = unwrapMessage;
		exports.sizeCaps = sizeCaps;
		exports.attachSchema = attachSchema;
		exports.finalizeIssue = finalizeIssue;
		exports.getSizableOrigin = getSizableOrigin;
		exports.codePointLength = codePointLength;
		exports.getLengthableOrigin = getLengthableOrigin;
		exports.parsedType = parsedType;
		exports.issue = issue;
		exports.cleanEnum = cleanEnum;
		exports.base64ToUint8Array = base64ToUint8Array;
		exports.uint8ArrayToBase64 = uint8ArrayToBase64;
		exports.base64urlToUint8Array = base64urlToUint8Array;
		exports.uint8ArrayToBase64url = uint8ArrayToBase64url;
		exports.hexToUint8Array = hexToUint8Array;
		exports.uint8ArrayToHex = uint8ArrayToHex;
		exports.members = members;
		exports.own = own;
		exports.hide = hide;
		exports.derived = derived;
		exports.defineLazyInternal = defineLazyInternal;
		exports.installLazyProp = installLazyProp;
		exports.constantCatch = constantCatch;
		const core_js_1 = /*@__PURE__*/ requireCore$1();
		// functions
		function assertEqual(val) {
		    return val;
		}
		function assertNotEqual(val) {
		    return val;
		}
		function toZod() {
		    return (schema) => schema;
		}
		function assertIs(_arg) { }
		function assertNever(_x) {
		    throw new Error("Unexpected value in exhaustive check");
		}
		function assert(_) { }
		function getEnumValues(entries) {
		    const numericValues = Object.values(entries).filter((v) => typeof v === "number");
		    const values = Object.entries(entries)
		        .filter(([k, _]) => numericValues.indexOf(+k) === -1)
		        .map(([_, v]) => v);
		    return values;
		}
		function joinValues(array, separator = "|") {
		    return array.map((val) => stringifyPrimitive(val)).join(separator);
		}
		function jsonStringifyReplacer(_, value) {
		    if (typeof value === "bigint")
		        return value.toString();
		    return value;
		}
		// the accessor lives on a shared prototype: an own accessor makes every box a dictionary-mode object (~360 B and a slow load per read against ~100 B and an inlined getter here)
		class Cached {
		    constructor(getter) {
		        this._getter = getter;
		        this._value = undefined;
		    }
		    get value() {
		        const getter = this._getter;
		        if (getter !== undefined) {
		            this._value = getter();
		            this._getter = undefined;
		        }
		        return this._value;
		    }
		}
		function cached(getter) {
		    return new Cached(getter);
		}
		function nullish(input) {
		    return input === null || input === undefined;
		}
		function cleanRegex(source) {
		    const start = source.startsWith("^") ? 1 : 0;
		    const end = source.endsWith("$") ? source.length - 1 : source.length;
		    return source.slice(start, end);
		}
		function floatSafeRemainder(val, step) {
		    const ratio = val / step;
		    const roundedRatio = Math.round(ratio);
		    // `val` and `step` each round to a double before the division rounds again, so a true decimal multiple's quotient can sit up to 1.5 of these scaled epsilons from the integer. A 1x tolerance therefore rejected 2.03 as a multiple of 0.07; 4x covers the worst case with margin.
		    const tolerance = 4 * Number.EPSILON * Math.max(Math.abs(ratio), 1);
		    if (Math.abs(ratio - roundedRatio) < tolerance)
		        return 0;
		    return ratio - roundedRatio;
		}
		const EVALUATING = /* @__PURE__*/ Symbol("evaluating");
		function defineLazy(object, key, getter) {
		    let value = undefined;
		    Object.defineProperty(object, key, {
		        get() {
		            if (value === EVALUATING) {
		                // Circular reference detected, return undefined to break the cycle
		                return undefined;
		            }
		            if (value === undefined) {
		                value = EVALUATING;
		                value = getter();
		            }
		            return value;
		        },
		        set(v) {
		            Object.defineProperty(object, key, {
		                value: v,
		                // configurable: true,
		            });
		            // object[key] = v;
		        },
		        configurable: true,
		    });
		}
		function objectClone(obj) {
		    return Object.create(Object.getPrototypeOf(obj), Object.getOwnPropertyDescriptors(obj));
		}
		function assignProp(target, prop, value) {
		    Object.defineProperty(target, prop, {
		        value,
		        writable: true,
		        enumerable: true,
		        configurable: true,
		    });
		}
		/**
		 * Whichever object a def's `shape` currently answers from: the one the caller passed until the first read, the frozen copy after it.
		 *
		 * Its keys and descriptors read without invoking anything, which is what lets a discriminated union check its discriminator, and the cycle walk read a shape, without resolving a getter that references the schema being constructed. A def that answers `shape` from an accessor of its own has none.
		 */
		function rawShape(def) {
		    const desc = Object.getOwnPropertyDescriptor(def, "shape");
		    return desc?.get ? desc.get.raw : desc?.value;
		}
		// where a builder reads its source's keys and descriptors, resolving only a shape a def answers for itself. A shape resolves by object spread, so only its enumerable keys are ever part of it.
		function sourceShape(schema) {
		    return rawShape(schema._zod.def) ?? schema._zod.def.shape;
		}
		// a key whose value is not settled yet, self-caching so every read after the first gets the same one
		function deferProp(target, key, getter) {
		    Object.defineProperty(target, key, {
		        get() {
		            const value = getter();
		            assignProp(this, key, value);
		            return value;
		        },
		        enumerable: true,
		        configurable: true,
		    });
		}
		// Writes a settled key. A plain assignment is much cheaper than `defineProperty` and produces the same descriptor, but it runs whatever setter already answers to the key — an accessor this shape deferred, or an inherited one, which `__proto__` has on every object and prototype pollution can add for any name.
		function putProp(target, key, value) {
		    if (key in target)
		        assignProp(target, key, value);
		    else
		        target[key] = value;
		}
		/**
		 * Copies `keys` of `source`'s shape onto `target`, each value passed through `wrap`.
		 *
		 * A key the source has resolved is copied through now, so the derived shape states it outright and nothing has to resolve it to learn what it holds. A key the source still defers stays deferred, and reads back through the source's own `shape`, so it resolves once and both shapes get that one schema.
		 */
		function mirrorShape(target, source, keys, wrap) {
		    const raw = sourceShape(source);
		    for (const key of keys) {
		        const desc = Object.getOwnPropertyDescriptor(raw, key);
		        if (!desc.enumerable)
		            continue;
		        if (desc.get) {
		            deferProp(target, key, () => {
		                const value = source._zod.def.shape[key];
		                return wrap ? wrap(value, key) : value;
		            });
		        }
		        else
		            putProp(target, key, wrap ? wrap(desc.value, key) : desc.value);
		    }
		}
		// same, for a plain shape a caller passed rather than a schema's
		function mirrorProps(target, source) {
		    for (const key of Reflect.ownKeys(source)) {
		        const desc = Object.getOwnPropertyDescriptor(source, key);
		        if (!desc.enumerable)
		            continue;
		        if (desc.get)
		            deferProp(target, key, () => source[key]);
		        else
		            putProp(target, key, desc.value);
		    }
		}
		function mergeDefs(...defs) {
		    const mergedDescriptors = {};
		    for (const def of defs) {
		        const descriptors = Object.getOwnPropertyDescriptors(def);
		        Object.assign(mergedDescriptors, descriptors);
		    }
		    return Object.defineProperties({}, mergedDescriptors);
		}
		function cloneDef(schema) {
		    return mergeDefs(schema._zod.def);
		}
		function getElementAtPath(obj, path) {
		    if (!path)
		        return obj;
		    return path.reduce((acc, key) => acc?.[key], obj);
		}
		function promiseAllObject(promisesObj) {
		    const keys = Object.keys(promisesObj);
		    const promises = keys.map((key) => promisesObj[key]);
		    return Promise.all(promises).then((results) => {
		        const resolvedObj = {};
		        for (let i = 0; i < keys.length; i++) {
		            resolvedObj[keys[i]] = results[i];
		        }
		        return resolvedObj;
		    });
		}
		function randomString(length = 10) {
		    const chars = "abcdefghijklmnopqrstuvwxyz";
		    let str = "";
		    for (let i = 0; i < length; i++) {
		        str += chars[Math.floor(Math.random() * chars.length)];
		    }
		    return str;
		}
		function esc(str) {
		    return JSON.stringify(str);
		}
		function slugify(input) {
		    return input
		        .toLowerCase()
		        .trim()
		        .replace(/[^\w\s-]/g, "")
		        .replace(/[\s_-]+/g, "-")
		        .replace(/^-+|-+$/g, "");
		}
		exports.captureStackTrace = ("captureStackTrace" in Error ? Error.captureStackTrace : (..._args) => { });
		function isObject(data) {
		    return typeof data === "object" && data !== null && !Array.isArray(data);
		}
		exports.allowsEval = cached(() => {
		    // Skip the probe under `jitless`: strict CSPs report the caught `new Function` as a `securitypolicyviolation` even though the throw is swallowed.
		    if (core_js_1.globalConfig.jitless) {
		        return false;
		    }
		    // @ts-ignore
		    if (typeof navigator !== "undefined" && navigator?.userAgent?.includes("Cloudflare")) {
		        return false;
		    }
		    try {
		        const F = Function;
		        new F("");
		        return true;
		    }
		    catch (_) {
		        return false;
		    }
		});
		function isPlainObject(o) {
		    if (isObject(o) === false)
		        return false;
		    // modified constructor
		    const ctor = o.constructor;
		    if (ctor === undefined)
		        return true;
		    if (typeof ctor !== "function")
		        return true;
		    // modified prototype
		    const prot = ctor.prototype;
		    if (isObject(prot) === false)
		        return false;
		    // ctor doesn't have static `isPrototypeOf`
		    if (Object.prototype.hasOwnProperty.call(prot, "isPrototypeOf") === false) {
		        return false;
		    }
		    return true;
		}
		function shallowClone(o) {
		    if (isPlainObject(o))
		        return { ...o };
		    if (Array.isArray(o))
		        return [...o];
		    if (o instanceof Map)
		        return new Map(o);
		    if (o instanceof Set)
		        return new Set(o);
		    return o;
		}
		function numKeys(data) {
		    let keyCount = 0;
		    for (const key in data) {
		        if (Object.prototype.hasOwnProperty.call(data, key)) {
		            keyCount++;
		        }
		    }
		    return keyCount;
		}
		const getParsedType = (data) => {
		    const t = typeof data;
		    switch (t) {
		        case "undefined":
		            return "undefined";
		        case "string":
		            return "string";
		        case "number":
		            return Number.isNaN(data) ? "nan" : "number";
		        case "boolean":
		            return "boolean";
		        case "function":
		            return "function";
		        case "bigint":
		            return "bigint";
		        case "symbol":
		            return "symbol";
		        case "object":
		            if (Array.isArray(data)) {
		                return "array";
		            }
		            if (data === null) {
		                return "null";
		            }
		            if (data.then && typeof data.then === "function" && data.catch && typeof data.catch === "function") {
		                return "promise";
		            }
		            if (typeof Map !== "undefined" && data instanceof Map) {
		                return "map";
		            }
		            if (typeof Set !== "undefined" && data instanceof Set) {
		                return "set";
		            }
		            if (typeof Date !== "undefined" && data instanceof Date) {
		                return "date";
		            }
		            // @ts-ignore
		            if (typeof File !== "undefined" && data instanceof File) {
		                return "file";
		            }
		            return "object";
		        default:
		            throw new Error(`Unknown data type: ${t}`);
		    }
		};
		exports.getParsedType = getParsedType;
		exports.propertyKeyTypes = new Set(["string", "number", "symbol"]);
		exports.primitiveTypes = new Set([
		    "string",
		    "number",
		    "bigint",
		    "boolean",
		    "symbol",
		    "undefined",
		]);
		function escapeRegex(str) {
		    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		}
		// zod-specific utils
		function clone(inst, def, params) {
		    const cl = new inst._zod.constr(def ?? inst._zod.def);
		    if (!def || params?.parent)
		        cl._zod.parent = inst;
		    return cl;
		}
		function normalizeParams(_params) {
		    const params = _params;
		    if (!params)
		        return {};
		    if (typeof params === "string")
		        return { error: () => params };
		    if (params?.message !== undefined) {
		        if (params?.error !== undefined)
		            throw new Error("Cannot specify both `message` and `error` params");
		        params.error = params.message;
		    }
		    delete params.message;
		    if (typeof params.error === "string")
		        return { ...params, error: () => params.error };
		    return params;
		}
		function createTransparentProxy(getter) {
		    let target;
		    return new Proxy({}, {
		        get(_, prop, receiver) {
		            target ?? (target = getter());
		            return Reflect.get(target, prop, receiver);
		        },
		        set(_, prop, value, receiver) {
		            target ?? (target = getter());
		            return Reflect.set(target, prop, value, receiver);
		        },
		        has(_, prop) {
		            target ?? (target = getter());
		            return Reflect.has(target, prop);
		        },
		        deleteProperty(_, prop) {
		            target ?? (target = getter());
		            return Reflect.deleteProperty(target, prop);
		        },
		        ownKeys(_) {
		            target ?? (target = getter());
		            return Reflect.ownKeys(target);
		        },
		        getOwnPropertyDescriptor(_, prop) {
		            target ?? (target = getter());
		            return Reflect.getOwnPropertyDescriptor(target, prop);
		        },
		        defineProperty(_, prop, descriptor) {
		            target ?? (target = getter());
		            return Reflect.defineProperty(target, prop, descriptor);
		        },
		    });
		}
		function stringifyPrimitive(value) {
		    if (typeof value === "bigint")
		        return value.toString() + "n";
		    if (typeof value === "string")
		        return `"${value}"`;
		    return `${value}`;
		}
		function optionalKeys(shape) {
		    return Object.keys(shape).filter((k) => {
		        return shape[k]._zod.optin !== undefined && shape[k]._zod.optout === "optional";
		    });
		}
		
		exports.NUMBER_FORMAT_RANGES = (() => ({
		    safeint: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
		    int32: [-2147483648, 2147483647],
		    uint32: [0, 4294967295],
		    float32: [-3.4028234663852886e38, 3.4028234663852886e38],
		    float64: [-Number.MAX_VALUE, Number.MAX_VALUE],
		}))();
		exports.BIGINT_FORMAT_RANGES = {
		    int64: [/* @__PURE__*/ BigInt("-9223372036854775808"), /* @__PURE__*/ BigInt("9223372036854775807")],
		    uint64: [/* @__PURE__*/ BigInt(0), /* @__PURE__*/ BigInt("18446744073709551615")],
		};
		function pick(schema, mask) {
		    const currDef = schema._zod.def;
		    const checks = currDef.checks;
		    const hasChecks = checks && checks.length > 0;
		    if (hasChecks) {
		        throw new Error(".pick() cannot be used on object schemas containing refinements");
		    }
		    const newShape = {};
		    mirrorShape(newShape, schema, maskedKeys(schema, mask));
		    return clone(schema, mergeDefs(currDef, { shape: newShape, checks: [] }));
		}
		// the mask keys that select something, checked against the source's shape without resolving it
		function maskedKeys(schema, mask) {
		    const raw = sourceShape(schema);
		    const keys = [];
		    // `for...in` skips symbols, so a symbol in the mask would select nothing
		    for (const key of Reflect.ownKeys(mask)) {
		        if (!Object.getOwnPropertyDescriptor(raw, key)?.enumerable) {
		            throw new Error(`Unrecognized key: "${String(key)}"`);
		        }
		        if (mask[key])
		            keys.push(key);
		    }
		    return keys;
		}
		function omit(schema, mask) {
		    const currDef = schema._zod.def;
		    const checks = currDef.checks;
		    const hasChecks = checks && checks.length > 0;
		    if (hasChecks) {
		        throw new Error(".omit() cannot be used on object schemas containing refinements");
		    }
		    const omitted = new Set(maskedKeys(schema, mask));
		    const newShape = {};
		    mirrorShape(newShape, schema, Reflect.ownKeys(sourceShape(schema)).filter((key) => !omitted.has(key)));
		    return clone(schema, mergeDefs(currDef, { shape: newShape, checks: [] }));
		}
		function extend(schema, shape) {
		    if (!isPlainObject(shape)) {
		        throw new Error("Invalid input to extend: expected a plain object");
		    }
		    const checks = schema._zod.def.checks;
		    const hasChecks = checks && checks.length > 0;
		    if (hasChecks) {
		        // Only throw if new shape overlaps with existing shape. Use getOwnPropertyDescriptor to check key existence without accessing values
		        const existingShape = sourceShape(schema);
		        for (const key of Reflect.ownKeys(shape)) {
		            if (Object.getOwnPropertyDescriptor(existingShape, key) !== undefined) {
		                throw new Error("Cannot overwrite keys on object schemas containing refinements. Use `.safeExtend()` instead.");
		            }
		        }
		    }
		    return clone(schema, mergeDefs(schema._zod.def, { shape: extended(schema, shape) }));
		}
		// the source's keys, then the caller's overlaid on top
		function extended(schema, shape) {
		    const newShape = {};
		    mirrorShape(newShape, schema, Reflect.ownKeys(sourceShape(schema)));
		    mirrorProps(newShape, shape);
		    return newShape;
		}
		function safeExtend(schema, shape) {
		    if (!isPlainObject(shape)) {
		        throw new Error("Invalid input to safeExtend: expected a plain object");
		    }
		    return clone(schema, mergeDefs(schema._zod.def, { shape: extended(schema, shape) }));
		}
		function merge(a, b) {
		    if (!b?._zod?.def) {
		        throw new Error("Invalid input to merge: expected an object schema. To merge a plain shape, use `.extend()`.");
		    }
		    if (a._zod.def.checks?.length) {
		        throw new Error(".merge() cannot be used on object schemas containing refinements. Use .safeExtend() instead.");
		    }
		    const newShape = {};
		    mirrorShape(newShape, a, Reflect.ownKeys(sourceShape(a)));
		    mirrorShape(newShape, b, Reflect.ownKeys(sourceShape(b)));
		    const def = mergeDefs(a._zod.def, {
		        shape: newShape,
		        get catchall() {
		            return b._zod.def.catchall;
		        },
		        checks: b._zod.def.checks ?? [],
		    });
		    return clone(a, def);
		}
		function partial(Class, schema, mask, name = "partial") {
		    const currDef = schema._zod.def;
		    const checks = currDef.checks;
		    const hasChecks = checks && checks.length > 0;
		    if (hasChecks) {
		        throw new Error(`.${name}() cannot be used on object schemas containing refinements`);
		    }
		    const selected = mask ? new Set(maskedKeys(schema, mask)) : undefined;
		    const newShape = {};
		    mirrorShape(newShape, schema, Reflect.ownKeys(sourceShape(schema)), Class &&
		        ((value, key) => (selected && !selected.has(key) ? value : new Class({ type: "optional", innerType: value }))));
		    return clone(schema, mergeDefs(schema._zod.def, { shape: newShape, checks: [] }));
		}
		function required(Class, schema, mask) {
		    const selected = mask ? new Set(maskedKeys(schema, mask)) : undefined;
		    const newShape = {};
		    mirrorShape(newShape, schema, Reflect.ownKeys(sourceShape(schema)), (value, key) => 
		    // overwrite with non-optional
		    selected && !selected.has(key) ? value : new Class({ type: "nonoptional", innerType: value }));
		    return clone(schema, mergeDefs(schema._zod.def, { shape: newShape }));
		}
		// invalid_type | too_big | too_small | invalid_format | not_multiple_of | unrecognized_keys | invalid_union | invalid_key | invalid_element | invalid_value | custom
		function aborted(x, startIndex = 0) {
		    if (x.aborted === true)
		        return true;
		    for (let i = startIndex; i < x.issues.length; i++) {
		        if (x.issues[i]?.continue !== true) {
		            return true;
		        }
		    }
		    return false;
		}
		// Checks for explicit abort (continue === false), as opposed to implicit abort (continue === undefined). Used to respect `abort: true` in .refine() even for checks that have a `when` function.
		function explicitlyAborted(x, startIndex = 0) {
		    if (x.aborted === true)
		        return true;
		    for (let i = startIndex; i < x.issues.length; i++) {
		        if (x.issues[i]?.continue === false) {
		            return true;
		        }
		    }
		    return false;
		}
		function prefixIssues(path, issues) {
		    return issues.map((iss) => {
		        var _a;
		        (_a = iss).path ?? (_a.path = []);
		        iss.path.unshift(path);
		        return iss;
		    });
		}
		function unwrapMessage(message) {
		    return typeof message === "string" ? message : message?.message;
		}
		/* A check holds no link back to the schema it is attached to — the same check instance is shared by every clone of that schema — so the owner is stamped onto the issues a check just raised, at the only point where both are in scope. Runs on the failure path only; `start` is the issue count from before the check ran. */
		/** The leading size checks of `defChecks` that set an upper bound, each paired with it. */
		function sizeCaps(defChecks) {
		    const caps = [];
		    for (const ch of defChecks ?? []) {
		        const d = ch._zod.def;
		        // a later check may rewrite the value
		        if (!/_(length|size)$|_equals$/.test(d.check))
		            break;
		        const max = d.maximum ?? d.length ?? d.size;
		        if (max >= 0)
		            caps.push([max, ch]);
		    }
		    return caps;
		}
		function attachSchema(issues, start, inst) {
		    var _a;
		    for (let i = start; i < issues.length; i++) {
		        (_a = issues[i]).schema ?? (_a.schema = inst);
		    }
		}
		function finalizeIssue(iss, ctx, config) {
		    var _a;
		    // A schema that raised an issue itself owns it outright, and outranks any stamp an enclosing check left in `attachSchema`. String formats and z.custom() are schema and check at once, so when they act as a check they defer to that stamp instead.
		    const traits = iss.inst?._zod?.traits;
		    if (traits?.has("$ZodType")) {
		        if (traits.has("$ZodCheck"))
		            (_a = iss).schema ?? (_a.schema = iss.inst);
		        else
		            iss.schema = iss.inst;
		    }
		    // Decreasing specificity, first map to return a message wins. `inst` is whatever raised the issue, so a check's own map outranks the owning schema's.
		    const schemaError = iss.schema !== iss.inst ? iss.schema?._zod.def?.error : undefined;
		    const message = iss.message
		        ? iss.message
		        : (unwrapMessage(iss.inst?._zod.def?.error?.(iss)) ??
		            unwrapMessage(schemaError?.(iss)) ??
		            unwrapMessage(ctx?.error?.(iss)) ??
		            unwrapMessage(config.customError?.(iss)) ??
		            unwrapMessage(config.localeError?.(iss)) ??
		            "Invalid input");
		    // an explicit own-key copy beats object rest with excluded keys, which v8 routes through a generic runtime call; Object.keys rather than for-in so an issue pushed with a prototype does not leak inherited keys, and an own __proto__ key is dropped rather than assigned through the setter
		    const full = {};
		    for (const k of Object.keys(iss)) {
		        if (k === "inst" || k === "schema" || k === "continue" || k === "input" || k === "__proto__")
		            continue;
		        full[k] = iss[k];
		    }
		    full.path ?? (full.path = []);
		    full.message = message;
		    if (ctx?.reportInput) {
		        full.input = iss.input;
		    }
		    return full;
		}
		function getSizableOrigin(input) {
		    if (input instanceof Set)
		        return "set";
		    if (input instanceof Map)
		        return "map";
		    // @ts-ignore
		    if (input instanceof File)
		        return "file";
		    return "unknown";
		}
		const highSurrogate = /[\uD800-\uDBFF]/;
		// Code points in `str`: a surrogate pair counts once, a lone surrogate as itself. Hand-rolled because the string iterator allocates and runs ~250x slower on this path; the regex probe exits ~50x quicker for a string with no astral characters.
		function codePointLength(str) {
		    const units = str.length;
		    if (!highSurrogate.test(str))
		        return units;
		    let count = units;
		    for (let i = 0; i < units - 1; i++) {
		        if ((str.charCodeAt(i) & 0xfc00) === 0xd800 && (str.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
		            count--;
		            i++;
		        }
		    }
		    return count;
		}
		function getLengthableOrigin(input) {
		    if (Array.isArray(input))
		        return "array";
		    if (typeof input === "string")
		        return "string";
		    return "unknown";
		}
		function parsedType(data) {
		    const t = typeof data;
		    switch (t) {
		        case "number": {
		            return Number.isNaN(data) ? "nan" : "number";
		        }
		        case "object": {
		            if (data === null) {
		                return "null";
		            }
		            if (Array.isArray(data)) {
		                return "array";
		            }
		            const obj = data;
		            if (obj && Object.getPrototypeOf(obj) !== Object.prototype && "constructor" in obj && obj.constructor) {
		                return obj.constructor.name;
		            }
		        }
		    }
		    return t;
		}
		function issue(...args) {
		    const [iss, input, inst] = args;
		    if (typeof iss === "string") {
		        return {
		            message: iss,
		            code: "custom",
		            input,
		            inst,
		        };
		    }
		    return { ...iss };
		}
		function cleanEnum(obj) {
		    return Object.entries(obj)
		        .filter(([k, _]) => {
		        // return true if NaN, meaning it's not a number, thus a string key
		        return Number.isNaN(Number.parseInt(k, 10));
		    })
		        .map((el) => el[1]);
		}
		// Codec utility functions
		function base64ToUint8Array(base64) {
		    const binaryString = atob(base64);
		    const bytes = new Uint8Array(binaryString.length);
		    for (let i = 0; i < binaryString.length; i++) {
		        bytes[i] = binaryString.charCodeAt(i);
		    }
		    return bytes;
		}
		function uint8ArrayToBase64(bytes) {
		    let binaryString = "";
		    for (let i = 0; i < bytes.length; i++) {
		        binaryString += String.fromCharCode(bytes[i]);
		    }
		    return btoa(binaryString);
		}
		function base64urlToUint8Array(base64url) {
		    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
		    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
		    return base64ToUint8Array(base64 + padding);
		}
		function uint8ArrayToBase64url(bytes) {
		    return uint8ArrayToBase64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
		}
		function hexToUint8Array(hex) {
		    const cleanHex = hex.replace(/^0x/, "");
		    if (cleanHex.length % 2 !== 0) {
		        throw new Error("Invalid hex string length");
		    }
		    const bytes = new Uint8Array(cleanHex.length / 2);
		    for (let i = 0; i < cleanHex.length; i += 2) {
		        bytes[i / 2] = Number.parseInt(cleanHex.slice(i, i + 2), 16);
		    }
		    return bytes;
		}
		function uint8ArrayToHex(bytes) {
		    return Array.from(bytes)
		        .map((b) => b.toString(16).padStart(2, "0"))
		        .join("");
		}
		// instanceof
		class Class {
		    constructor(..._args) { }
		}
		exports.Class = Class;
		//////////    PROTOTYPE INSTALLERS     //////////
		//
		// Members live on the prototype and materialize per instance on first read, which keeps own-property count under the step where V8 stops using inline slots. Changing anything here means re-measuring runtime, memory and bundle size together — see "The three axes" in AGENTS.md.
		/**
		 * Installs a trait's members on its prototype. Each value builds that member for the instance on first read; the built value shadows the accessor as an own property, so a detached `const { parse } = schema` keeps working.
		 *
		 * Call this from a `proto` initializer, which runs once per prototype — never per instance.
		 */
		function members(proto, table) {
		    for (const key in table) {
		        const desc = Object.getOwnPropertyDescriptor(table, key);
		        // a getter installs as written, so it stays live: `description` reads through to the registry on every access. not enumerable: an object literal's is, and a prototype member never was
		        if (desc.get)
		            Object.defineProperty(proto, key, { ...desc, enumerable: false });
		        // a method materializes bound on first read, which is what keeps a detached member working: `const opt = schema.optional; opt()`
		        else
		            defineBound(proto, key, desc.value);
		    }
		}
		/** Shadows a prototype member with an own value, so a getter that builds from the instance runs once. */
		function own(inst, key, value, enumerable = true) {
		    Object.defineProperty(inst, key, { configurable: true, writable: true, enumerable, value });
		    return value;
		}
		/** Like {@link own}, for a member that was never an own data property and has to stay out of `Object.keys`. */
		function hide(inst, key, value) {
		    return own(inst, key, value, false);
		}
		/** Adds members a table derives from the instance: each builds on first read and shadows as own data, and assignment shadows the same way, as when these were own properties. */
		function derived(computes, table) {
		    for (const key in computes) {
		        const compute = computes[key];
		        // an object literal's accessor is configurable and enumerable, and `members` copies the descriptor as written
		        Object.defineProperty(table, key, {
		            configurable: true,
		            enumerable: true,
		            get() {
		                return own(this, key, compute(this));
		            },
		            set(value) {
		                own(this, key, value);
		            },
		        });
		    }
		    return table;
		}
		function defineBound(proto, key, fn) {
		    Object.defineProperty(proto, key, {
		        configurable: true,
		        get() {
		            // vitest's spyOn calls a prototype getter bare to find the function it wraps, so a nullish receiver answers the raw method
		            return this == null ? fn : own(this, key, fn.bind(this));
		        },
		        set(value) {
		            own(this, key, value);
		        },
		    });
		}
		/** Returns the prototype to install on, or `undefined` if this group is already installed on it. */
		function claim(inst, sentinel) {
		    const proto = Object.getPrototypeOf(inst);
		    // Runs on every construction, so `in` rather than the costlier `hasOwnProperty.call`. Sentinels are keys the group itself defines.
		    return sentinel in proto ? undefined : proto;
		}
		// The internals whose init chain is installing. A second call for the same one is a derived constructor overriding its base, so it must not construct another schema in between or the override is dropped.
		let installing;
		// Set while a getter is running, so a value that resolved through a recursion break is not memoized. One shared descriptor shadows the key for the duration, which costs no per-key allocation.
		let broke = false;
		const breaker = {
		    configurable: true,
		    get() {
		        broke = true;
		        return undefined;
		    },
		};
		/**
		 * Installs a lazily-derived internal on the `_zod` prototype of `inst`'s
		 * constructor, computed from the internals object itself and cached there on
		 * first read. One accessor per constructor rather than one per instance.
		 */
		function defineLazyInternal(inst, key, compute) {
		    const proto = Object.getPrototypeOf(inst._zod);
		    if (key in proto && installing !== inst._zod) {
		        // A repeat construction: everything is installed already. Cleared here so the reference is not held past the first construction of every type.
		        installing = undefined;
		        return;
		    }
		    installing = inst._zod;
		    Object.defineProperty(proto, key, {
		        configurable: true,
		        get() {
		            // Shadowed before computing so a re-entrant read from a recursive schema resolves to undefined instead of running the getter again.
		            Object.defineProperty(this, key, breaker);
		            const outer = broke;
		            broke = false;
		            try {
		                const value = compute(this);
		                // A result that resolved through a recursion break is recomputed once the graph is complete; everything else memoizes, undefined included.
		                if (broke)
		                    delete this[key];
		                else
		                    Object.defineProperty(this, key, { configurable: true, writable: true, value });
		                broke = broke || outer;
		                return value;
		            }
		            catch (err) {
		                // A compute that threw memoizes nothing, so a later read runs it again and fails the same way. The shadow goes with it, since leaving it installed would answer undefined for every later read.
		                delete this[key];
		                broke = broke || outer;
		                throw err;
		            }
		        },
		        set(value) {
		            Object.defineProperty(this, key, { configurable: true, writable: true, value });
		        },
		    });
		}
		/**
		 * Installs `key` on `inst`'s prototype, computed by `make` on first read and cached there as an own
		 * data property. One accessor per constructor rather than one per instance, because an own accessor
		 * puts every instance after the first into v8 dictionary mode. The key doubles as the sentinel.
		 */
		function installLazyProp(inst, key, make, enumerable) {
		    const proto = claim(inst, key);
		    if (!proto)
		        return;
		    Object.defineProperty(proto, key, {
		        configurable: true,
		        get() {
		            // Shadowed before computing, so a re-entrant read from a self-referential shape resolves to undefined instead of running the getter again. A data property rather than an accessor: an own accessor is the dictionary-mode transition this exists to avoid.
		            const desc = { configurable: true, writable: true, enumerable, value: undefined };
		            Object.defineProperty(this, key, desc);
		            // a compute that throws leaves the shadow behind, so later reads answer undefined instead of re-throwing; `defineLazy` did the same, and `defineLazyInternal`'s delete-on-catch would cost bytes in every bundle for a case only a throwing user getter reaches
		            desc.value = make(this);
		            Object.defineProperty(this, key, desc);
		            return desc.value;
		        },
		        set(value) {
		            Object.defineProperty(this, key, { configurable: true, writable: true, enumerable, value });
		        },
		    });
		}
		/** Marks the thunk `_catch` synthesises for a constant catch value. `Function.length` cannot tell that thunk from a user callback — rest and defaulted parameters both report arity 0 — and a user callback reads `ctx.error`, whose issues only finalize correctly against the caller's per-parse error map. Provenance can say what arity cannot. A plain string key rather than `Symbol.for`, whose call at module scope no bundler can prove pure — the same shape that anchored `urlCanParse` into every build. */
		exports.CONSTANT_CATCH = "~constantCatch";
		/** Wraps a constant catch value in a thunk tagged with {@link CONSTANT_CATCH}. */
		function constantCatch(value) {
		    const fn = () => value;
		    fn[exports.CONSTANT_CATCH] = true;
		    return fn;
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (util$1));
	return util$1;
}

var hasRequiredCore$1;

function requireCore$1 () {
	if (hasRequiredCore$1) return core;
	hasRequiredCore$1 = 1;
	(function (exports) {
		var _a;
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.globalConfig = exports.$ZodEncodeError = exports.$ZodAsyncError = exports.$brand = exports.NEVER = void 0;
		exports.$constructor = $constructor;
		exports.config = config;
		const util_js_1 = /*@__PURE__*/ requireUtil$2();
		/** A special constant with type `never` */
		exports.NEVER = Object.freeze({
		    status: "aborted",
		});
		/* Shared descriptor for installing `_zod`; defineProperty reads it
		 * synchronously, so reusing one object avoids a per-instance allocation. */
		const _zodDesc = { value: undefined, enumerable: false };
		// null where suppressing the capture would be unrecoverable: `parse()` puts the frames back with `captureStackTrace`, so without it the throw would lose its stack. also latched to null once `stackTraceLimit` proves unassignable, which a realm can do at any point by hardening Error
		let _E = "captureStackTrace" in Error ? Error : null;
		// v8 captures a stack trace inside the Error constructor, which dominates a failed parse; costs only the frames, and parse() restores those. the constructor must RUN: Object.create is cheaper and passes instanceof, but Error.isError and util.types.isNativeError check an internal slot
		function newError(Definition) {
		    const E = _E;
		    if (E) {
		        const saved = E.stackTraceLimit;
		        if (typeof saved === "number") {
		            try {
		                E.stackTraceLimit = 0;
		            }
		            catch {
		                _E = null;
		                return new Definition();
		            }
		            try {
		                return new Definition();
		            }
		            finally {
		                E.stackTraceLimit = saved;
		            }
		        }
		    }
		    return new Definition();
		}
		function $constructor(name, initializer, 
		/** This trait's members, installed once on every prototype that composes it. They cannot be declared in the initializer above: that runs per instance, and the prototype is shared. */
		proto, params) {
		    // Prototype for this constructor's `_zod` internals. Lazily-derived fields (`values`, `pattern`, `optin`, …) install here once rather than as an accessor on every instance.
		    const zodProto = {};
		    // Assigning the fields in the constructor body is what gives instances in-object slots; building the object literally and reparenting it costs a second allocation and a generic property copy.
		    function Internals(def) {
		        this.def = def;
		        this.constr = _;
		        this.traits = new Set();
		    }
		    Internals.prototype = zodProto;
		    const protoMembers = proto;
		    // One trait's members land on every prototype whose chain composes it, so the answer is per prototype rather than per trait.
		    const initialized = protoMembers && new WeakSet();
		    function init(inst, def) {
		        if (!inst._zod) {
		            _zodDesc.value = new Internals(def);
		            try {
		                Object.defineProperty(inst, "_zod", _zodDesc);
		            }
		            finally {
		                // Cleared even on throw, so the shared descriptor never leaks one instance's internals into the next.
		                _zodDesc.value = undefined;
		            }
		        }
		        else if (inst._zod.traits.has(name)) {
		            return;
		        }
		        inst._zod.traits.add(name);
		        initializer(inst, def);
		        if (initialized) {
		            // `super(def)` from a user subclass gives `this` a prototype the subclass owns, and installing there would overwrite whatever the subclass declared. `constr` built the instance, so its prototype is the one below the subclass's that should carry the members. A receiver whose chain never reaches that prototype installs on its own, which for a plain object handed straight to `init` means `Object.prototype` — unchanged from before.
		            const own = Object.getPrototypeOf(inst);
		            const ctorProto = inst._zod.constr.prototype;
		            let up = own;
		            while (up && up !== ctorProto)
		                up = Object.getPrototypeOf(up);
		            const target = up ?? own;
		            if (!initialized.has(target)) {
		                initialized.add(target);
		                (0, util_js_1.members)(target, protoMembers);
		            }
		        }
		        // support prototype modifications; for-in avoids the array allocation of Object.keys on the (usually empty) prototype
		        const proto = _.prototype;
		        for (const k in proto) {
		            if (!Object.prototype.hasOwnProperty.call(proto, k))
		                continue;
		            if (!(k in inst)) {
		                inst[k] = proto[k].bind(inst);
		            }
		        }
		    }
		    // doesn't work if Parent has a constructor with arguments
		    const Parent = params?.Parent ?? Object;
		    class Definition extends Parent {
		    }
		    Object.defineProperty(Definition, "name", { value: name });
		    function _(def) {
		        const inst = params?.Parent ? newError(Definition) : this;
		        init(inst, def);
		        const deferred = inst._zod.deferred;
		        if (deferred) {
		            for (const fn of deferred) {
		                fn();
		            }
		            // Released: initializers run once, and the list would otherwise be retained for the schema's lifetime.
		            inst._zod.deferred = undefined;
		        }
		        // Global post-processor hook. Internal: installed by `import "zod/compile"` to enable AOT compilation for every constructed schema. Runs last, once the instance is fully built, because it hands the instance to compile(). The post-processor is expected to be reentrancy-guarded by its own implementation.
		        const pp = globalThis.__zod_globalConfig?.postProcessor;
		        if (pp)
		            pp(inst);
		        return inst;
		    }
		    Object.defineProperty(_, "init", { value: init });
		    Object.defineProperty(_, Symbol.hasInstance, {
		        value: (inst) => {
		            if (params?.Parent && inst instanceof params.Parent)
		                return true;
		            return inst?._zod?.traits?.has(name);
		        },
		    });
		    Object.defineProperty(_, "name", { value: name });
		    return _;
		}
		//////////////////////////////   UTILITIES   ///////////////////////////////////////
		exports.$brand = Symbol("zod_brand");
		class $ZodAsyncError extends Error {
		    constructor() {
		        super(`Encountered Promise during synchronous parse. Use .parseAsync() instead.`);
		    }
		}
		exports.$ZodAsyncError = $ZodAsyncError;
		class $ZodEncodeError extends Error {
		    constructor(name) {
		        super(`Encountered unidirectional transform during encode: ${name}`);
		        this.name = "ZodEncodeError";
		    }
		}
		exports.$ZodEncodeError = $ZodEncodeError;
		(_a = globalThis).__zod_globalConfig ?? (_a.__zod_globalConfig = {});
		exports.globalConfig = globalThis.__zod_globalConfig;
		function config(newConfig) {
		    if (newConfig)
		        Object.assign(exports.globalConfig, newConfig);
		    return exports.globalConfig;
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (core));
	return core;
}

var parse$3 = {};

var errors$2 = {};

var hasRequiredErrors$3;

function requireErrors$3 () {
	if (hasRequiredErrors$3) return errors$2;
	hasRequiredErrors$3 = 1;
	(function (exports) {
		var __createBinding = (errors$2 && errors$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (errors$2 && errors$2.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (errors$2 && errors$2.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.$ZodRealError = exports.$ZodError = void 0;
		exports.flattenError = flattenError;
		exports.formatError = formatError;
		exports.treeifyError = treeifyError;
		exports.toDotPath = toDotPath;
		exports.prettifyError = prettifyError;
		const core_js_1 = /*@__PURE__*/ requireCore$1();
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		/* Computing the message eagerly is expensive (pretty-printed JSON of all
		 * issues), so defer it until first read. The accessor functions and
		 * descriptors are shared across instances to keep error construction
		 * cheap; the computed message is cached on the internals object. The
		 * setter preserves plain assignment semantics for consumers that
		 * overwrite `message`. */
		function _getMessage() {
		    const internals = this._zod;
		    internals.message ?? (internals.message = JSON.stringify(internals.def, util.jsonStringifyReplacer, 2));
		    return internals.message;
		}
		function _setMessage(value) {
		    this._zod.message = value;
		}
		const _messageDesc = {
		    get: _getMessage,
		    set: _setMessage,
		    enumerable: true,
		    configurable: true,
		};
		const _issuesDesc = { value: undefined, enumerable: false };
		/* Prototypes that already carry the lazy `toString`. Seeded with the
		 * intrinsics so that `init` on a foreign object — it accepts any object —
		 * can never install an accessor onto a prototype we do not own. */
		const _installedToString = /* @__PURE__ */ new WeakSet([Object.prototype, Error.prototype]);
		const initializer = (inst, def) => {
		    inst.name = "$ZodError";
		    // `_zod` is already non-enumerable: $constructor's init defined it with this same descriptor
		    _issuesDesc.value = def;
		    Object.defineProperty(inst, "issues", _issuesDesc);
		    // Clear the shared slot; a retained `value` pins the last error's issues.
		    _issuesDesc.value = undefined;
		    Object.defineProperty(inst, "message", _messageDesc);
		    /* `toString` lives as a non-enumerable lazy getter on the shared
		     * prototype; on first access it caches a per-instance closure so
		     * detached usage still works. */
		    const proto = Object.getPrototypeOf(inst);
		    if (!_installedToString.has(proto)) {
		        _installedToString.add(proto);
		        Object.defineProperty(proto, "toString", {
		            configurable: true,
		            enumerable: false,
		            get() {
		                const value = () => this.message;
		                Object.defineProperty(this, "toString", { value, configurable: true, writable: true });
		                return value;
		            },
		            set(value) {
		                Object.defineProperty(this, "toString", { value, configurable: true, writable: true });
		            },
		        });
		    }
		};
		exports.$ZodError = (0, core_js_1.$constructor)("$ZodError", initializer);
		exports.$ZodRealError = (0, core_js_1.$constructor)("$ZodError", initializer, undefined, {
		    Parent: Error,
		});
		/** Get-or-create `obj[key]` as an own data property. A path segment naming an inherited member
		 * ("toString", "constructor") would otherwise read through to the prototype, and assigning
		 * "__proto__" would hit the setter instead of creating a key. */
		function node(obj, key, make) {
		    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
		        if (key === "__proto__") {
		            Object.defineProperty(obj, key, { value: make(), writable: true, enumerable: true, configurable: true });
		        }
		        else {
		            obj[key] = make();
		        }
		    }
		    return obj[key];
		}
		function flattenError(error, mapper = (issue) => issue.message) {
		    const fieldErrors = {};
		    const formErrors = [];
		    for (const sub of error.issues) {
		        if (sub.path.length > 0) {
		            node(fieldErrors, sub.path[0], () => []).push(mapper(sub));
		        }
		        else {
		            formErrors.push(mapper(sub));
		        }
		    }
		    return { formErrors, fieldErrors };
		}
		function formatError(error, mapper = (issue) => issue.message) {
		    const fieldErrors = { _errors: [] };
		    const processError = (error, path = []) => {
		        for (const issue of error.issues) {
		            if (issue.code === "invalid_union" && issue.errors.length) {
		                issue.errors.map((issues) => processError({ issues }, [...path, ...issue.path]));
		            }
		            else if (issue.code === "invalid_key") {
		                processError({ issues: issue.issues }, [...path, ...issue.path]);
		            }
		            else if (issue.code === "invalid_element") {
		                processError({ issues: issue.issues }, [...path, ...issue.path]);
		            }
		            else {
		                const fullpath = [...path, ...issue.path];
		                if (fullpath.length === 0) {
		                    fieldErrors._errors.push(mapper(issue));
		                }
		                else {
		                    let curr = fieldErrors;
		                    let i = 0;
		                    while (i < fullpath.length) {
		                        const el = fullpath[i];
		                        const terminal = i === fullpath.length - 1;
		                        // `_errors` is reserved by this legacy format, so merge a matching path segment into the current node instead of treating its array as a child.
		                        if (el === "_errors") {
		                            if (terminal)
		                                curr._errors.push(mapper(issue));
		                            i++;
		                            continue;
		                        }
		                        // A path element may collide with an inherited property name such as
		                        // "__proto__" or "constructor". Truthiness checks read the prototype
		                        // (so no node is created, then ._errors.push throws), and bracket
		                        // assignment of "__proto__" hits the setter instead of creating an
		                        // own key. Guard the read with hasOwnProperty and create the node
		                        // with defineProperty so any path element becomes a real own key.
		                        if (!Object.prototype.hasOwnProperty.call(curr, el)) {
		                            Object.defineProperty(curr, el, {
		                                value: { _errors: [] },
		                                enumerable: true,
		                                writable: true,
		                                configurable: true,
		                            });
		                        }
		                        const node = curr[el];
		                        if (terminal) {
		                            node._errors.push(mapper(issue));
		                        }
		                        curr = node;
		                        i++;
		                    }
		                }
		            }
		        }
		    };
		    processError(error);
		    return fieldErrors;
		}
		function treeifyError(error, mapper = (issue) => issue.message) {
		    const result = { errors: [] };
		    const processError = (error, path = []) => {
		        var _a;
		        for (const issue of error.issues) {
		            if (issue.code === "invalid_union" && issue.errors.length) {
		                // regular union error
		                issue.errors.map((issues) => processError({ issues }, [...path, ...issue.path]));
		            }
		            else if (issue.code === "invalid_key") {
		                processError({ issues: issue.issues }, [...path, ...issue.path]);
		            }
		            else if (issue.code === "invalid_element") {
		                processError({ issues: issue.issues }, [...path, ...issue.path]);
		            }
		            else {
		                const fullpath = [...path, ...issue.path];
		                if (fullpath.length === 0) {
		                    result.errors.push(mapper(issue));
		                    continue;
		                }
		                let curr = result;
		                let i = 0;
		                while (i < fullpath.length) {
		                    const el = fullpath[i];
		                    const terminal = i === fullpath.length - 1;
		                    if (typeof el === "string") {
		                        curr.properties ?? (curr.properties = {});
		                        // el may collide with an inherited property name ("__proto__",
		                        // "constructor", ...); ??= reads the prototype so the node is never
		                        // created and curr.errors.push throws. Guard with hasOwnProperty and
		                        // create the node with defineProperty so "__proto__" becomes a real
		                        // own key rather than invoking the prototype setter.
		                        if (!Object.prototype.hasOwnProperty.call(curr.properties, el)) {
		                            Object.defineProperty(curr.properties, el, {
		                                value: { errors: [] },
		                                enumerable: true,
		                                writable: true,
		                                configurable: true,
		                            });
		                        }
		                        curr = curr.properties[el];
		                    }
		                    else {
		                        curr.items ?? (curr.items = []);
		                        (_a = curr.items)[el] ?? (_a[el] = { errors: [] });
		                        curr = curr.items[el];
		                    }
		                    if (terminal) {
		                        curr.errors.push(mapper(issue));
		                    }
		                    i++;
		                }
		            }
		        }
		    };
		    processError(error);
		    return result;
		}
		/** Format a ZodError as a human-readable string in the following form.
		 *
		 * From
		 *
		 * ```ts
		 * ZodError {
		 *   issues: [
		 *     {
		 *       expected: 'string',
		 *       code: 'invalid_type',
		 *       path: [ 'username' ],
		 *       message: 'Invalid input: expected string'
		 *     },
		 *     {
		 *       expected: 'number',
		 *       code: 'invalid_type',
		 *       path: [ 'favoriteNumbers', 1 ],
		 *       message: 'Invalid input: expected number'
		 *     }
		 *   ];
		 * }
		 * ```
		 *
		 * to
		 *
		 * ```
		 * username
		 *   ✖ Expected number, received string at "username
		 * favoriteNumbers[0]
		 *   ✖ Invalid input: expected number
		 * ```
		 */
		function toDotPath(_path) {
		    const segs = [];
		    const path = _path.map((seg) => (typeof seg === "object" ? seg.key : seg));
		    for (const seg of path) {
		        if (typeof seg === "number")
		            segs.push(`[${seg}]`);
		        else if (typeof seg === "symbol")
		            segs.push(`[${JSON.stringify(String(seg))}]`);
		        else if (/[^\w$]/.test(seg))
		            segs.push(`[${JSON.stringify(seg)}]`);
		        else {
		            if (segs.length)
		                segs.push(".");
		            segs.push(seg);
		        }
		    }
		    return segs.join("");
		}
		function prettifyError(error) {
		    const lines = [];
		    // sort by path length
		    const issues = [...error.issues].sort((a, b) => (a.path ?? []).length - (b.path ?? []).length);
		    // Process each issue
		    for (const issue of issues) {
		        lines.push(`✖ ${issue.message}`);
		        if (issue.path?.length)
		            lines.push(`  → at ${toDotPath(issue.path)}`);
		    }
		    // Convert Map to formatted string
		    return lines.join("\n");
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (errors$2));
	return errors$2;
}

var hasRequiredParse$4;

function requireParse$4 () {
	if (hasRequiredParse$4) return parse$3;
	hasRequiredParse$4 = 1;
	(function (exports) {
		var __createBinding = (parse$3 && parse$3.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (parse$3 && parse$3.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (parse$3 && parse$3.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.safeDecodeAsync = exports._safeDecodeAsync = exports.safeEncodeAsync = exports._safeEncodeAsync = exports.safeDecode = exports._safeDecode = exports.safeEncode = exports._safeEncode = exports.decodeAsync = exports._decodeAsync = exports.encodeAsync = exports._encodeAsync = exports.decode = exports._decode = exports.encode = exports._encode = exports.validateAsync = exports.validate = exports.safeParseAsync = exports._safeParseAsync = exports.safeParse = exports._safeParse = exports.parseAsync = exports._parseAsync = exports.parse = exports._parse = void 0;
		const core = __importStar(/*@__PURE__*/ requireCore$1());
		const errors = __importStar(/*@__PURE__*/ requireErrors$3());
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		// Always both keys, so the `_params` read site in `_parse` sees one object shape rather than two.
		function finalizeParams(callee, params) {
		    return { callee: params?.callee ?? callee, Err: params?.Err };
		}
		const _parse = (_Err) => {
		    const fn = (schema, value, _ctx, _params) => {
		        const ctx = _ctx ? { ..._ctx, async: false } : { async: false };
		        const result = schema._zod.run({ value, issues: [] }, ctx);
		        if (result instanceof Promise) {
		            throw new core.$ZodAsyncError();
		        }
		        if (result.issues.length) {
		            const e = new (_params?.Err ?? _Err)(result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())));
		            util.captureStackTrace(e, _params?.callee ?? fn);
		            throw e;
		        }
		        return result.value;
		    };
		    return fn;
		};
		exports._parse = _parse;
		exports.parse = (0, exports._parse)(errors.$ZodRealError);
		const _parseAsync = (_Err) => {
		    const fn = async (schema, value, _ctx, params) => {
		        const ctx = _ctx ? { ..._ctx, async: true } : { async: true };
		        let result = schema._zod.run({ value, issues: [] }, ctx);
		        if (result instanceof Promise)
		            result = await result;
		        if (result.issues.length) {
		            const e = new (params?.Err ?? _Err)(result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())));
		            util.captureStackTrace(e, params?.callee ?? fn);
		            throw e;
		        }
		        return result.value;
		    };
		    return fn;
		};
		exports._parseAsync = _parseAsync;
		exports.parseAsync = (0, exports._parseAsync)(errors.$ZodRealError);
		const _safeParse = (_Err) => (schema, value, _ctx) => {
		    const ctx = _ctx ? { ..._ctx, async: false } : { async: false };
		    const result = schema._zod.run({ value, issues: [] }, ctx);
		    if (result instanceof Promise) {
		        throw new core.$ZodAsyncError();
		    }
		    return result.issues.length ? failure(_Err, result.issues, ctx) : { success: true, data: result.value };
		};
		exports._safeParse = _safeParse;
		exports.safeParse = (0, exports._safeParse)(errors.$ZodRealError);
		// the error is built on the first read of `error`: finalizing the issues and constructing the instance is most of a failing parse, and a caller that only branches on `success` never pays it. a getter in the literal keeps this small; the alternative, one shared accessor descriptor plus a hidden state slot, reads ~15% faster but costs ~75 B gzipped in every bundle
		function failure(Err, issues, ctx) {
		    let error;
		    return {
		        success: false,
		        get error() {
		            if (!error) {
		                error = new Err(issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())));
		                // finalizeIssue drops `input`, so the built error holds nothing; keeping the raw issues past this point pins the parsed value for the life of the result
		                issues = undefined;
		                ctx = undefined;
		            }
		            return error;
		        },
		        set error(e) {
		            error = e;
		            // a replacement makes the getter's branch unreachable, so the captures have to go here too
		            issues = undefined;
		            ctx = undefined;
		        },
		    };
		}
		const _safeParseAsync = (_Err) => async (schema, value, _ctx) => {
		    const ctx = _ctx ? { ..._ctx, async: true } : { async: true };
		    let result = schema._zod.run({ value, issues: [] }, ctx);
		    if (result instanceof Promise)
		        result = await result;
		    return result.issues.length ? failure(_Err, result.issues, ctx) : { success: true, data: result.value };
		};
		exports._safeParseAsync = _safeParseAsync;
		exports.safeParseAsync = (0, exports._safeParseAsync)(errors.$ZodRealError);
		// registry mirrors of the compiler's sentinels, so this module never imports the compiler
		const COMPILE_INVALID = /* @__PURE__ */ Symbol.for("zod.compile.invalid");
		const COMPILE_FALLBACK = /* @__PURE__ */ Symbol.for("zod.compile.fallback");
		// Deliberately tiny, because v8 will not inline a body carrying the fallback's object literals and throw. Everything that is not the compiled happy path lives in validateFallback, and that split is worth ~35% on a compiled schema.
		exports.validate = ((schema, value, _ctx) => {
		    const validator = schema._zod.bag.validator;
		    if (validator !== undefined) {
		        if (validator(value) !== COMPILE_INVALID)
		            return true;
		        // a definite sentinel means the runtime would reject, so skip the re-parse; a ctx can still change the answer
		        if (validator.definite === true && _ctx === undefined)
		            return false;
		    }
		    return validateFallback(schema, value, _ctx);
		});
		function validateFallback(schema, value, _ctx) {
		    const ctx = _ctx
		        ? { ..._ctx, async: false, abortEarly: true }
		        : { async: false, abortEarly: true };
		    const fallbackRun = schema._zod.bag.fallbackRun;
		    let result;
		    if (fallbackRun) {
		        // skip nested fast paths on the fallback, so user callbacks keep the at-most-twice bound
		        ctx[COMPILE_FALLBACK] = true;
		        result = fallbackRun({ value, issues: [] }, ctx);
		    }
		    else {
		        result = schema._zod.run({ value, issues: [] }, ctx);
		    }
		    if (result instanceof Promise) {
		        throw new core.$ZodAsyncError();
		    }
		    return result.issues.length === 0;
		}
		// no fast path: the compiler keeps async parses on the runtime, because a promise-returning callback that is not declared async compiles to a throw
		const validateAsync = async (schema, value, _ctx) => {
		    const ctx = _ctx
		        ? { ..._ctx, async: true, abortEarly: true }
		        : { async: true, abortEarly: true };
		    let result = schema._zod.run({ value, issues: [] }, ctx);
		    if (result instanceof Promise)
		        result = await result;
		    return result.issues.length === 0;
		};
		exports.validateAsync = validateAsync;
		const _encode = (_Err) => {
		    const parse = (0, exports._parse)(_Err);
		    const fn = (schema, value, _ctx, _params) => {
		        const ctx = _ctx ? { ..._ctx, direction: "backward" } : { direction: "backward" };
		        return parse(schema, value, ctx, finalizeParams(fn, _params));
		    };
		    return fn;
		};
		exports._encode = _encode;
		exports.encode = (0, exports._encode)(errors.$ZodRealError);
		const _decode = (_Err) => {
		    const parse = (0, exports._parse)(_Err);
		    const fn = (schema, value, _ctx, _params) => {
		        return parse(schema, value, _ctx, finalizeParams(fn, _params));
		    };
		    return fn;
		};
		exports._decode = _decode;
		exports.decode = (0, exports._decode)(errors.$ZodRealError);
		const _encodeAsync = (_Err) => {
		    const parseAsync = (0, exports._parseAsync)(_Err);
		    const fn = async (schema, value, _ctx, _params) => {
		        const ctx = _ctx ? { ..._ctx, direction: "backward" } : { direction: "backward" };
		        return (await parseAsync(schema, value, ctx, finalizeParams(fn, _params)));
		    };
		    return fn;
		};
		exports._encodeAsync = _encodeAsync;
		exports.encodeAsync = (0, exports._encodeAsync)(errors.$ZodRealError);
		const _decodeAsync = (_Err) => {
		    const parseAsync = (0, exports._parseAsync)(_Err);
		    const fn = async (schema, value, _ctx, _params) => {
		        return await parseAsync(schema, value, _ctx, finalizeParams(fn, _params));
		    };
		    return fn;
		};
		exports._decodeAsync = _decodeAsync;
		exports.decodeAsync = (0, exports._decodeAsync)(errors.$ZodRealError);
		const _safeEncode = (_Err) => (schema, value, _ctx) => {
		    const ctx = _ctx ? { ..._ctx, direction: "backward" } : { direction: "backward" };
		    return (0, exports._safeParse)(_Err)(schema, value, ctx);
		};
		exports._safeEncode = _safeEncode;
		exports.safeEncode = (0, exports._safeEncode)(errors.$ZodRealError);
		const _safeDecode = (_Err) => (schema, value, _ctx) => {
		    return (0, exports._safeParse)(_Err)(schema, value, _ctx);
		};
		exports._safeDecode = _safeDecode;
		exports.safeDecode = (0, exports._safeDecode)(errors.$ZodRealError);
		const _safeEncodeAsync = (_Err) => async (schema, value, _ctx) => {
		    const ctx = _ctx ? { ..._ctx, direction: "backward" } : { direction: "backward" };
		    return (0, exports._safeParseAsync)(_Err)(schema, value, ctx);
		};
		exports._safeEncodeAsync = _safeEncodeAsync;
		exports.safeEncodeAsync = (0, exports._safeEncodeAsync)(errors.$ZodRealError);
		const _safeDecodeAsync = (_Err) => async (schema, value, _ctx) => {
		    return (0, exports._safeParseAsync)(_Err)(schema, value, _ctx);
		};
		exports._safeDecodeAsync = _safeDecodeAsync;
		exports.safeDecodeAsync = (0, exports._safeDecodeAsync)(errors.$ZodRealError);

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (parse$3));
	return parse$3;
}

var schemas$1 = {};

var checks$1 = {};

var regexes = {};

var hasRequiredRegexes;

function requireRegexes () {
	if (hasRequiredRegexes) return regexes;
	hasRequiredRegexes = 1;
	(function (exports) {
		var __createBinding = (regexes && regexes.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (regexes && regexes.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (regexes && regexes.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.sha1_base64 = exports.sha1_hex = exports.md5_base64url = exports.md5_base64 = exports.md5_hex = exports.hex = exports.uppercase = exports.lowercase = exports.undefined = exports.null = exports.boolean = exports.number = exports.integer = exports.bigint = exports.string = exports.anyString = exports.date = exports.iban = exports.currencyCode = exports.creditCard = exports.e164 = exports.httpProtocol = exports.domain = exports.hostname = exports.base64url = exports.base64 = exports.cidrv6 = exports.cidrv4 = exports.mac = exports.ipv6 = exports.ipv4 = exports.browserEmail = exports.idnEmail = exports.unicodeEmail = exports.rfc5322Email = exports.html5Email = exports.email = exports.uuid7 = exports.uuid6 = exports.uuid4 = exports.uuid = exports.guid = exports.extendedDuration = exports.duration = exports.nanoid = exports.ksuid = exports.xid = exports.ulid = exports.cuid2 = exports.cuid = void 0;
		exports.sha512_base64url = exports.sha512_base64 = exports.sha512_hex = exports.sha384_base64url = exports.sha384_base64 = exports.sha384_hex = exports.sha256_base64url = exports.sha256_base64 = exports.sha256_hex = exports.sha1_base64url = void 0;
		exports.nanoidOfLength = nanoidOfLength;
		exports.emoji = emoji;
		exports.time = time;
		exports.datetime = datetime;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		/**
		 * @deprecated CUID v1 is deprecated by its authors due to information leakage
		 * (timestamps embedded in the id). Use {@link cuid2} instead.
		 * See https://github.com/paralleldrive/cuid.
		 */
		exports.cuid = /^[cC][0-9a-z]{6,}$/;
		exports.cuid2 = /^[0-9a-z]+$/;
		exports.ulid = /^[0-7][0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{25}$/;
		exports.xid = /^[0-9a-vA-V]{20}$/;
		exports.ksuid = /^[A-Za-z0-9]{27}$/;
		exports.nanoid = /^[a-zA-Z0-9_-]{21}$/;
		function nanoidOfLength(length) {
		    return new RegExp(`^[a-zA-Z0-9_-]{${length}}$`);
		}
		/** ISO 8601-1 duration regex. Does not support the 8601-2 extensions like negative durations or fractional/negative components. */
		exports.duration = /^P(?:(\d+W)|(?!.*W)(?=\d|T\d)(\d+Y)?(\d+M)?(\d+D)?(T(?=\d)(\d+H)?(\d+M)?(\d+([.,]\d+)?S)?)?)$/;
		/** Implements ISO 8601-2 extensions like explicit +- prefixes, mixing weeks with other units, and fractional/negative components. */
		exports.extendedDuration = /^[-+]?P(?!$)(?:(?:[-+]?\d+Y)|(?:[-+]?\d+[.,]\d+Y$))?(?:(?:[-+]?\d+M)|(?:[-+]?\d+[.,]\d+M$))?(?:(?:[-+]?\d+W)|(?:[-+]?\d+[.,]\d+W$))?(?:(?:[-+]?\d+D)|(?:[-+]?\d+[.,]\d+D$))?(?:T(?=[\d+-])(?:(?:[-+]?\d+H)|(?:[-+]?\d+[.,]\d+H$))?(?:(?:[-+]?\d+M)|(?:[-+]?\d+[.,]\d+M$))?(?:[-+]?\d+(?:[.,]\d+)?S)?)??$/;
		/** A regex for any UUID-like identifier: 8-4-4-4-12 hex pattern */
		exports.guid = /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;
		/** Returns a regex for validating an RFC 9562/4122 UUID.
		 *
		 * @param version Optionally specify a version 1-8. If no version is specified, all versions are supported. */
		const uuid = (version) => {
		    if (!version)
		        return /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/;
		    return new RegExp(`^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-${version}[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})$`);
		};
		exports.uuid = uuid;
		exports.uuid4 = (0, exports.uuid)(4);
		exports.uuid6 = (0, exports.uuid)(6);
		exports.uuid7 = (0, exports.uuid)(7);
		/** Practical email validation */
		exports.email = /^(?:[A-Za-z0-9_'+\-]+\.)*[A-Za-z0-9_'+\-]*[A-Za-z0-9_+-]@(?:[A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;
		/** Equivalent to the HTML5 input[type=email] validation implemented by browsers. Source: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email */
		exports.html5Email = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
		/** The classic emailregex.com regex for RFC 5322-compliant emails */
		exports.rfc5322Email = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		/** A loose regex that allows Unicode characters, enforces length limits, and that's about it. */
		exports.unicodeEmail = /^[^\s@"]{1,64}@[^\s@]{1,255}$/u;
		exports.idnEmail = exports.unicodeEmail;
		exports.browserEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
		// from https://thekevinscott.com/emojis-in-javascript/#writing-a-regular-expression
		// Single character class, not an alternation: the two properties overlap (U+1F9B0-U+1F9B3), so `(A|B)+` backtracks exponentially on a failed match. The leading lookahead then demands one anchor — a pictograph, a regional indicator, or the enclosing keycap — because `\p{Emoji_Component}` on its own covers ASCII digits, `#`, `*`, ZWJ, variation selectors and skin tone modifiers, none of which is an emoji without a base.
		const _emoji = `^(?=[\\s\\S]*[\\p{Extended_Pictographic}\\p{Regional_Indicator}\\u20E3])[\\p{Extended_Pictographic}\\p{Emoji_Component}]+$`;
		function emoji() {
		    return new RegExp(_emoji, "u");
		}
		exports.ipv4 = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/;
		exports.ipv6 = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;
		const mac = (delimiter) => {
		    const escapedDelim = util.escapeRegex(delimiter ?? ":");
		    return new RegExp(`^(?:[0-9A-F]{2}${escapedDelim}){5}[0-9A-F]{2}$|^(?:[0-9a-f]{2}${escapedDelim}){5}[0-9a-f]{2}$`);
		};
		exports.mac = mac;
		exports.cidrv4 = /^((25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\/([0-9]|[1-2][0-9]|3[0-2])$/;
		exports.cidrv6 = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))\/(12[0-8]|1[01][0-9]|[1-9]?[0-9])$/;
		// https://stackoverflow.com/questions/7860392/determine-if-string-is-in-base64-using-javascript
		exports.base64 = /^$|^(?:[0-9a-zA-Z+/]{4})*(?:(?:[0-9a-zA-Z+/]{2}==)|(?:[0-9a-zA-Z+/]{3}=))?$/;
		exports.base64url = /^(?:[A-Za-z0-9_-]{4})*(?:[A-Za-z0-9_-]{2,3})?$/;
		// based on https://stackoverflow.com/questions/106179/regular-expression-to-match-dns-hostname-or-ip-address
		// export const hostname: RegExp = /^([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+$/;
		exports.hostname = /^(?=.{1,253}\.?$)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[-0-9a-zA-Z]{0,61}[0-9a-zA-Z])?)*\.?$/;
		exports.domain = /^(?=.{1,253}$)([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;
		exports.httpProtocol = /^https?$/;
		// https://blog.stevenlevithan.com/archives/validate-phone-number#r4-3 (regex sans spaces) E.164: leading digit must be 1-9; total digits (excluding '+') between 7-15
		exports.e164 = /^\+[1-9]\d{6,14}$/;
		// Credit card shape: 12–19 digits, optionally separated by single spaces or single hyphens. ISO/IEC 7812 caps the PAN at 19 digits; 12 is the shortest issued length (Maestro).
		exports.creditCard = /^\d(?:[ -]?\d){11,18}$/;
		// ISO 4217 alpha codes from the SIX list, regenerated by scripts/update-iso-4217.ts
		exports.currencyCode = /^(?:AED|AFN|ALL|AMD|AOA|ARS|AUD|AWG|AZN|BAM|BBD|BDT|BHD|BIF|BMD|BND|BOB|BOV|BRL|BSD|BTN|BWP|BYN|BZD|CAD|CDF|CHE|CHF|CHW|CLF|CLP|CNY|COP|COU|CRC|CUP|CVE|CZK|DJF|DKK|DOP|DZD|EGP|ERN|ETB|EUR|FJD|FKP|GBP|GEL|GHS|GIP|GMD|GNF|GTQ|GYD|HKD|HNL|HTG|HUF|IDR|ILS|INR|IQD|IRR|ISK|JMD|JOD|JPY|KES|KGS|KHR|KMF|KPW|KRW|KWD|KYD|KZT|LAK|LBP|LKR|LRD|LSL|LYD|MAD|MDL|MGA|MKD|MMK|MNT|MOP|MRU|MUR|MVR|MWK|MXN|MXV|MYR|MZN|NAD|NGN|NIO|NOK|NPR|NZD|OMR|PAB|PEN|PGK|PHP|PKR|PLN|PYG|QAR|RON|RSD|RUB|RWF|SAR|SBD|SCR|SDG|SEK|SGD|SHP|SLE|SOS|SRD|SSP|STN|SVC|SYP|SZL|THB|TJS|TMT|TND|TOP|TRY|TTD|TWD|TZS|UAH|UGX|USD|USN|UYI|UYU|UYW|UZS|VED|VES|VND|VUV|WST|XAD|XAF|XAG|XAU|XBA|XBB|XBC|XBD|XCD|XCG|XDR|XOF|XPD|XPF|XPT|XSU|XTS|XUA|XXX|YER|ZAR|ZMW|ZWG)$/;
		// iban electronic format: 2-letter country, check digits 02-98 (the only values `98 - remainder` can produce), 11-30 bban characters
		exports.iban = /^[A-Z]{2}(?!00|01|99)\d{2}[A-Z0-9]{11,30}$/;
		const dateSource = `(?:(?:\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\\d|30)|(?:02)-(?:0[1-9]|1\\d|2[0-8])))`;
		
		function anchor(source) {
		    return new RegExp(`^${source}$`);
		}
		exports.date = anchor(dateSource);
		function timeSource(args) {
		    const hhmm = `(?:[01]\\d|2[0-3]):[0-5]\\d`;
		    const regex = typeof args.precision === "number"
		        ? args.precision === -1
		            ? `${hhmm}`
		            : args.precision === 0
		                ? `${hhmm}:[0-5]\\d`
		                : `${hhmm}:[0-5]\\d\\.\\d{${args.precision}}`
		        : args.seconds
		            ? `${hhmm}:[0-5]\\d(?:\\.\\d+)?`
		            : `${hhmm}(?::[0-5]\\d(?:\\.\\d+)?)?`;
		    return regex;
		}
		function time(args) {
		    return new RegExp(`^${timeSource(args)}$`);
		}
		// Adapted from https://stackoverflow.com/a/3143231
		function datetime(args) {
		    const opts = ["Z"];
		    // if (args.offset) opts.push(`([+-]\\d{2}:\\d{2})`);
		    if (args.offset)
		        opts.push(`([+-](?:[01]\\d|2[0-3]):[0-5]\\d)`);
		    // RFC 3339 mandates seconds wherever the time carries a `Z` or an offset, so only the unqualified form `local` adds may omit them
		    const qualified = `${timeSource({ precision: args.precision, seconds: true })}(?:${opts.join("|")})`;
		    const timeRegex = args.local ? `${qualified}|${timeSource({ precision: args.precision })}` : qualified;
		    return new RegExp(`^${dateSource}T(?:${timeRegex})$`);
		}
		// the unbounded form of `string()` as a literal, so every plain string shares one instance instead of building its own
		exports.anyString = /^[\s\S]{0,}$/;
		const string = (params) => {
		    const regex = params ? `[\\s\\S]{${params?.minimum ?? 0},${params?.maximum ?? ""}}` : `[\\s\\S]*`;
		    return new RegExp(`^${regex}$`);
		};
		exports.string = string;
		exports.bigint = /^-?\d+n?$/;
		exports.integer = /^-?\d+$/;
		exports.number = /^-?\d+(?:\.\d+)?$/;
		exports.boolean = /^(?:true|false)$/i;
		const _null = /^null$/i;
		exports.null = _null;
		const _undefined = /^undefined$/i;
		exports.undefined = _undefined;
		// regex for string with no uppercase letters
		exports.lowercase = /^[^A-Z]*$/;
		// regex for string with no lowercase letters
		exports.uppercase = /^[^a-z]*$/;
		// regex for hexadecimal strings (any length)
		exports.hex = /^[0-9a-fA-F]*$/;
		// Hash regexes for different algorithms and encodings
		// Helper function to create base64 regex with exact length and padding
		function fixedBase64(bodyLength, padding) {
		    return new RegExp(`^[A-Za-z0-9+/]{${bodyLength}}${padding}$`);
		}
		// Helper function to create base64url regex with exact length (no padding)
		function fixedBase64url(length) {
		    return new RegExp(`^[A-Za-z0-9_-]{${length}}$`);
		}
		// MD5 (16 bytes): base64 = 24 chars total (22 + "==")
		exports.md5_hex = /^[0-9a-fA-F]{32}$/;
		exports.md5_base64 = fixedBase64(22, "==");
		exports.md5_base64url = fixedBase64url(22);
		// SHA1 (20 bytes): base64 = 28 chars total (27 + "=")
		exports.sha1_hex = /^[0-9a-fA-F]{40}$/;
		exports.sha1_base64 = fixedBase64(27, "=");
		exports.sha1_base64url = fixedBase64url(27);
		// SHA256 (32 bytes): base64 = 44 chars total (43 + "=")
		exports.sha256_hex = /^[0-9a-fA-F]{64}$/;
		exports.sha256_base64 = fixedBase64(43, "=");
		exports.sha256_base64url = fixedBase64url(43);
		// SHA384 (48 bytes): base64 = 64 chars total (no padding)
		exports.sha384_hex = /^[0-9a-fA-F]{96}$/;
		exports.sha384_base64 = fixedBase64(64, "");
		exports.sha384_base64url = fixedBase64url(64);
		// SHA512 (64 bytes): base64 = 88 chars total (86 + "==")
		exports.sha512_hex = /^[0-9a-fA-F]{128}$/;
		exports.sha512_base64 = fixedBase64(86, "==");
		exports.sha512_base64url = fixedBase64url(86);

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (regexes));
	return regexes;
}

var hasRequiredChecks$1;

function requireChecks$1 () {
	if (hasRequiredChecks$1) return checks$1;
	hasRequiredChecks$1 = 1;
	(function (exports) {
		// import { $ZodType } from "./schemas.js";
		var __createBinding = (checks$1 && checks$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (checks$1 && checks$1.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (checks$1 && checks$1.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.$ZodCheckOverwrite = exports.$ZodCheckMimeType = exports.$ZodCheckProperties = exports.$ZodCheckProperty = exports.$ZodCheckEndsWith = exports.$ZodCheckStartsWith = exports.$ZodCheckIncludes = exports.$ZodCheckUpperCase = exports.$ZodCheckLowerCase = exports.$ZodCheckRegex = exports.$ZodCheckStringFormat = exports.$ZodCheckLengthEquals = exports.$ZodCheckMinLength = exports.$ZodCheckMaxLength = exports.$ZodCheckSizeEquals = exports.$ZodCheckMinSize = exports.$ZodCheckMaxSize = exports.$ZodCheckBigIntFormat = exports.$ZodCheckNumberFormat = exports.$ZodCheckMultipleOf = exports.$ZodCheckGreaterThan = exports.$ZodCheckLessThan = exports.$ZodCheck = void 0;
		const core = __importStar(/*@__PURE__*/ requireCore$1());
		const regexes = __importStar(/*@__PURE__*/ requireRegexes());
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		exports.$ZodCheck = core.$constructor("$ZodCheck", (inst, def) => {
		    var _a;
		    inst._zod ?? (inst._zod = {});
		    inst._zod.def = def;
		    (_a = inst._zod).onattach ?? (_a.onattach = []);
		});
		/** Default `when` for size-based checks: run only on non-nullish values with a `size`. */
		const _whenHasSize = (payload) => {
		    const val = payload.value;
		    return !util.nullish(val) && val.size !== undefined;
		};
		/** Default `when` for length-based checks: run only on non-nullish values with a `length`. */
		const _whenHasLength = (payload) => {
		    const val = payload.value;
		    return !util.nullish(val) && val.length !== undefined;
		};
		const numericOriginMap = {
		    number: "number",
		    bigint: "bigint",
		    object: "date",
		};
		exports.$ZodCheckLessThan = core.$constructor("$ZodCheckLessThan", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    const origin = numericOriginMap[typeof def.value];
		    inst._zod.check = (payload) => {
		        if (def.inclusive ? payload.value <= def.value : payload.value < def.value) {
		            return;
		        }
		        payload.issues.push({
		            origin: numericOriginMap[typeof payload.value] ?? origin,
		            code: "too_big",
		            maximum: typeof def.value === "object" ? def.value.getTime() : def.value,
		            input: payload.value,
		            inclusive: def.inclusive,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckGreaterThan = core.$constructor("$ZodCheckGreaterThan", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    const origin = numericOriginMap[typeof def.value];
		    inst._zod.check = (payload) => {
		        if (def.inclusive ? payload.value >= def.value : payload.value > def.value) {
		            return;
		        }
		        payload.issues.push({
		            origin: numericOriginMap[typeof payload.value] ?? origin,
		            code: "too_small",
		            minimum: typeof def.value === "object" ? def.value.getTime() : def.value,
		            input: payload.value,
		            inclusive: def.inclusive,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckMultipleOf = 
		/*@__PURE__*/ core.$constructor("$ZodCheckMultipleOf", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (typeof payload.value !== typeof def.value)
		            throw new Error("Cannot mix number and bigint in multiple_of check.");
		        const isMultiple = typeof payload.value === "bigint"
		            ? // `value % 0n` throws, and nothing is a multiple of zero — the number branch already fails this way via NaN
		                def.value !== BigInt(0) && payload.value % def.value === BigInt(0)
		            : util.floatSafeRemainder(payload.value, def.value) === 0;
		        if (isMultiple)
		            return;
		        payload.issues.push({
		            origin: typeof payload.value,
		            code: "not_multiple_of",
		            divisor: def.value,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckNumberFormat = core.$constructor("$ZodCheckNumberFormat", (inst, def) => {
		    exports.$ZodCheck.init(inst, def); // no format checks
		    def.format = def.format || "float64";
		    const isInt = def.format?.includes("int");
		    const origin = isInt ? "int" : "number";
		    const [minimum, maximum] = util.NUMBER_FORMAT_RANGES[def.format];
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        if (isInt) {
		            if (!Number.isInteger(input)) {
		                // invalid_format issue
		                // payload.issues.push({
		                //   expected: def.format,
		                //   format: def.format,
		                //   code: "invalid_format",
		                //   input,
		                //   inst,
		                // });
		                // invalid_type issue
		                payload.issues.push({
		                    expected: origin,
		                    format: def.format,
		                    code: "invalid_type",
		                    continue: false,
		                    input,
		                    inst,
		                });
		                return;
		                // not_multiple_of issue
		                // payload.issues.push({
		                //   code: "not_multiple_of",
		                //   origin: "number",
		                //   input,
		                //   inst,
		                //   divisor: 1,
		                // });
		            }
		            if (!Number.isSafeInteger(input)) {
		                if (input > 0) {
		                    // too_big
		                    payload.issues.push({
		                        input,
		                        code: "too_big",
		                        maximum: Number.MAX_SAFE_INTEGER,
		                        note: "Integers must be within the safe integer range.",
		                        inst,
		                        origin,
		                        inclusive: true,
		                        continue: !def.abort,
		                    });
		                }
		                else {
		                    // too_small
		                    payload.issues.push({
		                        input,
		                        code: "too_small",
		                        minimum: Number.MIN_SAFE_INTEGER,
		                        note: "Integers must be within the safe integer range.",
		                        inst,
		                        origin,
		                        inclusive: true,
		                        continue: !def.abort,
		                    });
		                }
		                return;
		            }
		        }
		        if (input < minimum) {
		            payload.issues.push({
		                origin: "number",
		                input,
		                code: "too_small",
		                minimum,
		                inclusive: true,
		                inst,
		                continue: !def.abort,
		            });
		        }
		        if (input > maximum) {
		            payload.issues.push({
		                origin: "number",
		                input,
		                code: "too_big",
		                maximum,
		                inclusive: true,
		                inst,
		                continue: !def.abort,
		            });
		        }
		    };
		});
		exports.$ZodCheckBigIntFormat = core.$constructor("$ZodCheckBigIntFormat", (inst, def) => {
		    exports.$ZodCheck.init(inst, def); // no format checks
		    const [minimum, maximum] = util.BIGINT_FORMAT_RANGES[def.format];
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        if (input < minimum) {
		            payload.issues.push({
		                origin: "bigint",
		                input,
		                code: "too_small",
		                minimum: minimum,
		                inclusive: true,
		                inst,
		                continue: !def.abort,
		            });
		        }
		        if (input > maximum) {
		            payload.issues.push({
		                origin: "bigint",
		                input,
		                code: "too_big",
		                maximum,
		                inclusive: true,
		                inst,
		                continue: !def.abort,
		            });
		        }
		    };
		});
		exports.$ZodCheckMaxSize = core.$constructor("$ZodCheckMaxSize", (inst, def) => {
		    var _a;
		    exports.$ZodCheck.init(inst, def);
		    (_a = inst._zod.def).when ?? (_a.when = _whenHasSize);
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const size = input.size;
		        if (size <= def.maximum)
		            return;
		        payload.issues.push({
		            origin: util.getSizableOrigin(input),
		            code: "too_big",
		            maximum: def.maximum,
		            inclusive: true,
		            input,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckMinSize = core.$constructor("$ZodCheckMinSize", (inst, def) => {
		    var _a;
		    exports.$ZodCheck.init(inst, def);
		    (_a = inst._zod.def).when ?? (_a.when = _whenHasSize);
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const size = input.size;
		        if (size >= def.minimum)
		            return;
		        payload.issues.push({
		            origin: util.getSizableOrigin(input),
		            code: "too_small",
		            minimum: def.minimum,
		            inclusive: true,
		            input,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckSizeEquals = core.$constructor("$ZodCheckSizeEquals", (inst, def) => {
		    var _a;
		    exports.$ZodCheck.init(inst, def);
		    (_a = inst._zod.def).when ?? (_a.when = _whenHasSize);
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const size = input.size;
		        if (size === def.size)
		            return;
		        const tooBig = size > def.size;
		        payload.issues.push({
		            origin: util.getSizableOrigin(input),
		            ...(tooBig ? { code: "too_big", maximum: def.size } : { code: "too_small", minimum: def.size }),
		            inclusive: true,
		            exact: true,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckMaxLength = core.$constructor("$ZodCheckMaxLength", (inst, def) => {
		    var _a;
		    exports.$ZodCheck.init(inst, def);
		    (_a = inst._zod.def).when ?? (_a.when = _whenHasLength);
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const units = input.length;
		        // Strings are measured in Unicode code points, not UTF-16 units. A code point is at most two units, so a string that already fits in units fits in code points; only an overflow has to be counted.
		        const length = typeof input === "string" && units > def.maximum ? util.codePointLength(input) : units;
		        if (length <= def.maximum)
		            return;
		        const origin = util.getLengthableOrigin(input);
		        payload.issues.push({
		            origin,
		            code: "too_big",
		            maximum: def.maximum,
		            inclusive: true,
		            input,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckMinLength = core.$constructor("$ZodCheckMinLength", (inst, def) => {
		    var _a;
		    exports.$ZodCheck.init(inst, def);
		    (_a = inst._zod.def).when ?? (_a.when = _whenHasLength);
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const units = input.length;
		        // A code point is one or two UTF-16 units, so fewer units than the floor can never reach it and twice the floor always clears it. Only in between is the exact count in doubt.
		        const length = typeof input === "string" && units >= def.minimum && units < def.minimum * 2
		            ? util.codePointLength(input)
		            : units;
		        if (length >= def.minimum)
		            return;
		        const origin = util.getLengthableOrigin(input);
		        payload.issues.push({
		            origin,
		            code: "too_small",
		            minimum: def.minimum,
		            inclusive: true,
		            input,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckLengthEquals = core.$constructor("$ZodCheckLengthEquals", (inst, def) => {
		    var _a;
		    exports.$ZodCheck.init(inst, def);
		    (_a = inst._zod.def).when ?? (_a.when = _whenHasLength);
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const units = input.length;
		        // A code point is one or two UTF-16 units, so outside `[length, length * 2]` units the target is missed either way — and missed in the same direction in both measures.
		        const length = typeof input === "string" && units >= def.length && units <= def.length * 2
		            ? util.codePointLength(input)
		            : units;
		        if (length === def.length)
		            return;
		        const origin = util.getLengthableOrigin(input);
		        const tooBig = length > def.length;
		        payload.issues.push({
		            origin,
		            ...(tooBig ? { code: "too_big", maximum: def.length } : { code: "too_small", minimum: def.length }),
		            inclusive: true,
		            exact: true,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckStringFormat = core.$constructor("$ZodCheckStringFormat", (inst, def) => {
		    var _a, _b;
		    exports.$ZodCheck.init(inst, def);
		    if (def.pattern)
		        (_a = inst._zod).check ?? (_a.check = (payload) => {
		            def.pattern.lastIndex = 0;
		            if (def.pattern.test(payload.value))
		                return;
		            payload.issues.push({
		                origin: "string",
		                code: "invalid_format",
		                format: def.format,
		                input: payload.value,
		                ...(def.pattern ? { pattern: def.pattern.toString() } : {}),
		                inst,
		                continue: !def.abort,
		            });
		        });
		    else
		        (_b = inst._zod).check ?? (_b.check = () => { });
		});
		exports.$ZodCheckRegex = core.$constructor("$ZodCheckRegex", (inst, def) => {
		    exports.$ZodCheckStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        def.pattern.lastIndex = 0;
		        if (def.pattern.test(payload.value))
		            return;
		        payload.issues.push({
		            origin: "string",
		            code: "invalid_format",
		            format: "regex",
		            input: payload.value,
		            pattern: def.pattern.toString(),
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckLowerCase = core.$constructor("$ZodCheckLowerCase", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.lowercase);
		    exports.$ZodCheckStringFormat.init(inst, def);
		});
		exports.$ZodCheckUpperCase = core.$constructor("$ZodCheckUpperCase", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.uppercase);
		    exports.$ZodCheckStringFormat.init(inst, def);
		});
		exports.$ZodCheckIncludes = core.$constructor("$ZodCheckIncludes", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    const escapedRegex = util.escapeRegex(def.includes);
		    // `String.prototype.includes(sub, position)` matches `sub` at `position`
		    // OR LATER, so the pattern must allow at least `position` leading chars
		    // (`{N,}`), not exactly `position` chars (`{N}`).
		    const pattern = new RegExp(typeof def.position === "number" ? `^.{${def.position},}${escapedRegex}` : escapedRegex);
		    def.pattern = pattern;
		    inst._zod.check = (payload) => {
		        if (payload.value.includes(def.includes, def.position))
		            return;
		        payload.issues.push({
		            origin: "string",
		            code: "invalid_format",
		            format: "includes",
		            includes: def.includes,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckStartsWith = core.$constructor("$ZodCheckStartsWith", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    const pattern = new RegExp(`^${util.escapeRegex(def.prefix)}.*`);
		    def.pattern ?? (def.pattern = pattern);
		    inst._zod.check = (payload) => {
		        if (payload.value.startsWith(def.prefix))
		            return;
		        payload.issues.push({
		            origin: "string",
		            code: "invalid_format",
		            format: "starts_with",
		            prefix: def.prefix,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckEndsWith = core.$constructor("$ZodCheckEndsWith", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    const pattern = new RegExp(`.*${util.escapeRegex(def.suffix)}$`);
		    def.pattern ?? (def.pattern = pattern);
		    inst._zod.check = (payload) => {
		        if (payload.value.endsWith(def.suffix))
		            return;
		        payload.issues.push({
		            origin: "string",
		            code: "invalid_format",
		            format: "ends_with",
		            suffix: def.suffix,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		///////////////////////////////////
		/////    $ZodCheckProperty    /////
		///////////////////////////////////
		function handleCheckPropertyResult(result, payload, property) {
		    if (result.issues.length) {
		        payload.issues.push(...util.prefixIssues(property, result.issues));
		    }
		}
		exports.$ZodCheckProperty = core.$constructor("$ZodCheckProperty", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    inst._zod.check = (payload) => {
		        const result = def.schema._zod.run({
		            value: payload.value[def.property],
		            issues: [],
		        }, {});
		        if (result instanceof Promise) {
		            return result.then((result) => handleCheckPropertyResult(result, payload, def.property));
		        }
		        handleCheckPropertyResult(result, payload, def.property);
		        return;
		    };
		});
		exports.$ZodCheckProperties = core.$constructor("$ZodCheckProperties", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    util.hide(inst, Symbol.iterator, function* () {
		        yield inst;
		    });
		    // key and schema snapshotted together: reading one live and the other cached lets a later mutation of the caller's shape object pair a stale key with a missing schema
		    let entries;
		    inst._zod.check = (payload) => {
		        // the base schema already typed the value, so only a nullish one is rejected here: the properties read on a primitive too, matching z.property() on a string's length
		        if (payload.value == null) {
		            payload.issues.push({ expected: "object", code: "invalid_type", input: payload.value, inst });
		            return undefined;
		        }
		        entries ?? (entries = Reflect.ownKeys(def.shape).map((key) => [key, def.shape[key]]));
		        const input = payload.value;
		        let proms;
		        for (const [key, schema] of entries) {
		            const result = schema._zod.run({ value: input[key], issues: [] }, {});
		            if (result instanceof Promise) {
		                proms ?? (proms = []);
		                proms.push(result.then((result) => handleCheckPropertyResult(result, payload, key)));
		            }
		            else {
		                handleCheckPropertyResult(result, payload, key);
		            }
		        }
		        if (proms)
		            return Promise.all(proms).then(() => undefined);
		        return undefined;
		    };
		});
		exports.$ZodCheckMimeType = core.$constructor("$ZodCheckMimeType", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    const mimeSet = new Set(def.mime);
		    inst._zod.check = (payload) => {
		        if (mimeSet.has(payload.value.type))
		            return;
		        payload.issues.push({
		            code: "invalid_value",
		            values: def.mime,
		            input: payload.value.type,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCheckOverwrite = core.$constructor("$ZodCheckOverwrite", (inst, def) => {
		    exports.$ZodCheck.init(inst, def);
		    inst._zod.check = (payload) => {
		        payload.value = def.tx(payload.value);
		    };
		});

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (checks$1));
	return checks$1;
}

var doc = {};

var hasRequiredDoc;

function requireDoc () {
	if (hasRequiredDoc) return doc;
	hasRequiredDoc = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.Doc = void 0;
		class Doc {
		    constructor(args = [], closed = {}) {
		        this.content = [];
		        this.indent = 0;
		        this.args = args;
		        this.closed = closed;
		    }
		    // the compiler catches a child's throw and keeps writing into this doc, so the indent has to unwind with it
		    indented(fn) {
		        this.indent += 1;
		        try {
		            fn(this);
		        }
		        finally {
		            this.indent -= 1;
		        }
		    }
		    write(arg) {
		        if (typeof arg === "function") {
		            arg(this, { execution: "sync" });
		            arg(this, { execution: "async" });
		            return;
		        }
		        const content = arg;
		        const lines = content.split("\n").filter((x) => x);
		        const minIndent = Math.min(...lines.map((x) => x.length - x.trimStart().length));
		        const dedented = lines.map((x) => x.slice(minIndent)).map((x) => " ".repeat(this.indent * 2) + x);
		        for (const line of dedented) {
		            this.content.push(line);
		        }
		    }
		    compile() {
		        const F = Function;
		        const content = this?.content ?? [``];
		        const factory = new F(...Object.keys(this.closed), `return function (${this.args.join(", ")}) {\n${content.join("\n")}\n};`);
		        return factory(...Object.values(this.closed));
		    }
		}
		exports.Doc = Doc;

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (doc));
	return doc;
}

var versions$1 = {};

var hasRequiredVersions;

function requireVersions () {
	if (hasRequiredVersions) return versions$1;
	hasRequiredVersions = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.version = void 0;
		exports.version = {
		    major: 4,
		    minor: 6,
		    patch: 7,
		};

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (versions$1));
	return versions$1;
}

var hasRequiredSchemas$1;

function requireSchemas$1 () {
	if (hasRequiredSchemas$1) return schemas$1;
	hasRequiredSchemas$1 = 1;
	(function (exports) {
		var __createBinding = (schemas$1 && schemas$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (schemas$1 && schemas$1.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (schemas$1 && schemas$1.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.$ZodObject = exports.$ZodArray = exports.$ZodDate = exports.$ZodVoid = exports.$ZodNever = exports.$ZodUnknown = exports.$ZodAny = exports.$ZodNull = exports.$ZodUndefined = exports.$ZodSymbol = exports.$ZodBigIntFormat = exports.$ZodBigInt = exports.$ZodBoolean = exports.$ZodNumberFormat = exports.$ZodNumber = exports.$ZodCustomStringFormat = exports.$ZodJWT = exports.$ZodIBAN = exports.$ZodCreditCard = exports.$ZodE164 = exports.$ZodBase64URL = exports.base64urlCharset = exports.$ZodBase64 = exports.base64Charset = exports.$ZodCIDRv6 = exports.$ZodCIDRv4 = exports.$ZodMAC = exports.$ZodIPv6 = exports.$ZodIPv4 = exports.$ZodISODuration = exports.$ZodISOTime = exports.$ZodISODate = exports.$ZodISODateTime = exports.$ZodKSUID = exports.$ZodXID = exports.$ZodULID = exports.$ZodCUID2 = exports.$ZodCUID = exports.$ZodNanoID = exports.$ZodEmoji = exports.$ZodURL = exports.URL_UNPARSEABLE = exports.URL_BAD_FORMAT = exports.$ZodEmail = exports.$ZodUUID = exports.$ZodGUID = exports.$ZodStringFormat = exports.$ZodString = exports.clone = exports.$ZodType = void 0;
		exports.$ZodCustom = exports.$ZodLazy = exports.$ZodPromise = exports.$ZodFunction = exports.$ZodTemplateLiteral = exports.$ZodReadonly = exports.$ZodPreprocess = exports.$ZodCodec = exports.$ZodPipe = exports.$ZodNaN = exports.$ZodCatch = exports.$ZodSuccess = exports.$ZodNonOptional = exports.$ZodPrefault = exports.$ZodDefault = exports.$ZodNullable = exports.$ZodExactOptional = exports.$ZodOptional = exports.$ZodTransform = exports.$ZodFile = exports.$ZodLiteral = exports.$ZodEnum = exports.$ZodSet = exports.$ZodMap = exports.$ZodRecord = exports.$ZodTuple = exports.$ZodIntersection = exports.$ZodDiscriminatedUnion = exports.$ZodXor = exports.$ZodUnion = exports.$ZodObjectJIT = void 0;
		exports.standardProps = standardProps;
		exports.canParseURL = canParseURL;
		exports.validateURL = validateURL;
		exports.parseURLObject = parseURLObject;
		exports.stripTabAndNewline = stripTabAndNewline;
		exports.urlHostnameOk = urlHostnameOk;
		exports.urlProtocolOk = urlProtocolOk;
		exports.isValidIPv6 = isValidIPv6;
		exports.isValidCIDRv6 = isValidCIDRv6;
		exports.isValidBase64 = isValidBase64;
		exports.isValidBase64URL = isValidBase64URL;
		exports.isValidCreditCard = isValidCreditCard;
		exports.isValidIBAN = isValidIBAN;
		exports.isValidJWT = isValidJWT;
		exports.getDiscriminatedOption = getDiscriminatedOption;
		exports.mergeValues = mergeValues;
		const checks = __importStar(/*@__PURE__*/ requireChecks$1());
		const core = __importStar(/*@__PURE__*/ requireCore$1());
		const doc_js_1 = /*@__PURE__*/ requireDoc();
		const parse_js_1 = /*@__PURE__*/ requireParse$4();
		const regexes = __importStar(/*@__PURE__*/ requireRegexes());
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const versions_js_1 = /*@__PURE__*/ requireVersions();
		exports.$ZodType = core.$constructor("$ZodType", (inst, def) => {
		    var _a;
		    inst ?? (inst = {});
		    inst._zod.def = def; // set _def property
		    inst._zod.bag = inst._zod.bag || {}; // initialize _bag object
		    inst._zod.version = versions_js_1.version;
		    const defChecks = inst._zod.def.checks;
		    // if inst is itself a checks.$ZodCheck, run it as a check
		    const checks = inst._zod.traits.has("$ZodCheck")
		        ? [inst, ...(defChecks ?? [])]
		        : defChecks?.length
		            ? [...defChecks]
		            : [];
		    for (const ch of checks) {
		        for (const fn of ch._zod.onattach) {
		            fn(inst);
		        }
		    }
		    if (checks.length === 0) {
		        // deferred initializer inst._zod.parse is not yet defined
		        (_a = inst._zod).deferred ?? (_a.deferred = []);
		        inst._zod.deferred?.push(() => {
		            inst._zod.run = inst._zod.parse;
		        });
		    }
		    else {
		        const runChecks = (payload, checks, ctx) => {
		            if (payload.memo)
		                return payload;
		            let isAborted = util.aborted(payload);
		            let asyncResult;
		            for (const ch of checks) {
		                if (ch._zod.def.when) {
		                    if (util.explicitlyAborted(payload))
		                        continue;
		                    const shouldRun = ch._zod.def.when(payload);
		                    if (!shouldRun)
		                        continue;
		                }
		                else if (isAborted) {
		                    continue;
		                }
		                const currLen = payload.issues.length;
		                const _ = ch._zod.check(payload);
		                if (_ instanceof Promise && ctx?.async === false) {
		                    throw new core.$ZodAsyncError();
		                }
		                if (asyncResult || _ instanceof Promise) {
		                    asyncResult = (asyncResult ?? Promise.resolve()).then(async () => {
		                        await _;
		                        const nextLen = payload.issues.length;
		                        if (nextLen === currLen)
		                            return;
		                        util.attachSchema(payload.issues, currLen, inst);
		                        if (!isAborted)
		                            isAborted = util.aborted(payload, currLen);
		                    });
		                }
		                else {
		                    const nextLen = payload.issues.length;
		                    if (nextLen === currLen)
		                        continue;
		                    util.attachSchema(payload.issues, currLen, inst);
		                    if (!isAborted)
		                        isAborted = util.aborted(payload, currLen);
		                }
		            }
		            if (asyncResult) {
		                return asyncResult.then(() => {
		                    return payload;
		                });
		            }
		            return payload;
		        };
		        const handleCanaryResult = (canary, payload, ctx) => {
		            // abort if the canary is aborted
		            if (util.aborted(canary)) {
		                canary.aborted = true;
		                return canary;
		            }
		            // run checks first, then
		            const checkResult = runChecks(payload, checks, ctx);
		            if (checkResult instanceof Promise) {
		                if (ctx.async === false)
		                    throw new core.$ZodAsyncError();
		                return checkResult.then((checkResult) => inst._zod.parse(checkResult, ctx));
		            }
		            return inst._zod.parse(checkResult, ctx);
		        };
		        inst._zod.run = (payload, ctx) => {
		            if (ctx.skipChecks) {
		                return inst._zod.parse(payload, ctx);
		            }
		            if (ctx.direction === "backward") {
		                // run canary initial pass (no checks)
		                const canary = inst._zod.parse({ value: payload.value, issues: [] }, { ...ctx, skipChecks: true });
		                if (canary instanceof Promise) {
		                    return canary.then((canary) => {
		                        return handleCanaryResult(canary, payload, ctx);
		                    });
		                }
		                return handleCanaryResult(canary, payload, ctx);
		            }
		            // forward
		            const result = inst._zod.parse(payload, ctx);
		            if (result instanceof Promise) {
		                if (ctx.async === false)
		                    throw new core.$ZodAsyncError();
		                return result.then((result) => runChecks(result, checks, ctx));
		            }
		            return runChecks(result, checks, ctx);
		        };
		    }
		}, {
		    // Wrappers extend this by installing a richer factory over it; reading it eagerly would defeat the laziness.
		    get "~standard"() {
		        return util.hide(this, "~standard", standardProps(this));
		    },
		    set "~standard"(value) {
		        util.own(this, "~standard", value);
		    },
		});
		/** The Standard Schema surface for `inst`. Shared so wrappers can extend it without forcing it. */
		// a Standard Schema result only reports issues, so a failure finalizes them straight off the raw payload: no ZodError, and no lazy result to read through
		const toStandardResult = (r, ctx) => r.issues.length ? { issues: r.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())) } : { value: r.value };
		async function validateAsync(inst, value) {
		    const ctx = { async: true };
		    return toStandardResult((await inst._zod.run({ value, issues: [] }, ctx)), ctx);
		}
		function standardProps(inst) {
		    return {
		        validate: (value) => {
		            const ctx = { async: false };
		            try {
		                const r = inst._zod.run({ value, issues: [] }, ctx);
		                if (!(r instanceof Promise))
		                    return toStandardResult(r, ctx);
		            }
		            catch (_) { }
		            // async function so a synchronously throwing check rejects instead of escaping validate
		            return validateAsync(inst, value);
		        },
		        vendor: "zod",
		        version: 1,
		    };
		}
		var util_js_1 = /*@__PURE__*/ requireUtil$2();
		Object.defineProperty(exports, "clone", { enumerable: true, get: function () { return util_js_1.clone; } });
		exports.$ZodString = core.$constructor("$ZodString", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    // a format's own pattern, else unbounded; a template literal derives the check-aware form itself
		    inst._zod.pattern = def.pattern ?? regexes.anyString;
		    inst._zod.parse = (payload, _) => {
		        if (def.coerce)
		            try {
		                payload.value = String(payload.value);
		            }
		            catch (_) { }
		        if (typeof payload.value === "string")
		            return payload;
		        payload.issues.push({
		            expected: "string",
		            code: "invalid_type",
		            input: payload.value,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodStringFormat = core.$constructor("$ZodStringFormat", (inst, def) => {
		    // check initialization must come first
		    checks.$ZodCheckStringFormat.init(inst, def);
		    exports.$ZodString.init(inst, def);
		});
		exports.$ZodGUID = core.$constructor("$ZodGUID", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.guid);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodUUID = core.$constructor("$ZodUUID", (inst, def) => {
		    if (def.version) {
		        const versionMap = {
		            v1: 1,
		            v2: 2,
		            v3: 3,
		            v4: 4,
		            v5: 5,
		            v6: 6,
		            v7: 7,
		            v8: 8,
		        };
		        const v = versionMap[def.version];
		        if (v === undefined)
		            throw new Error(`Invalid UUID version: "${def.version}"`);
		        def.pattern ?? (def.pattern = regexes.uuid(v));
		    }
		    else
		        def.pattern ?? (def.pattern = regexes.uuid());
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodEmail = core.$constructor("$ZodEmail", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.email);
		    exports.$ZodStringFormat.init(inst, def);
		});
		/** The `://` guard rejected the input before the URL constructor saw it. */
		exports.URL_BAD_FORMAT = 1;
		/** The URL parser rejected the input. */
		exports.URL_UNPARSEABLE = 2;
		function canParseURL(input) {
		    try {
		        if (typeof URL !== "undefined" && typeof URL.canParse === "function")
		            return URL.canParse(input);
		        new URL(input);
		        return true;
		    }
		    catch {
		        return false;
		    }
		}
		function validateURL(trimmed, def) {
		    if (!("normalize" in def) && !("hostname" in def) && !("protocol" in def)) {
		        return canParseURL(trimmed) || exports.URL_UNPARSEABLE;
		    }
		    return parseURLObject(trimmed, def);
		}
		/** Parses a URL while preserving the non-normalizing HTTP guard. */
		function parseURLObject(trimmed, def) {
		    // When normalize is off, require :// for http/https URLs. This prevents strings like "http:example.com" or "https:/path" from being silently accepted
		    if (!def.normalize && def.protocol?.source === regexes.httpProtocol.source && !/^https?:\/\//i.test(trimmed)) {
		        return exports.URL_BAD_FORMAT;
		    }
		    try {
		        if (typeof URL !== "undefined") {
		            const URLStatic = URL;
		            if (typeof URLStatic.parse === "function")
		                return URLStatic.parse(trimmed) ?? exports.URL_UNPARSEABLE;
		        }
		        // @ts-ignore
		        return new URL(trimmed);
		    }
		    catch {
		        return exports.URL_UNPARSEABLE;
		    }
		}
		const asciiTabOrNewline = /[\t\n\r]/g;
		/** The URL parser deletes every ASCII tab, LF and CR from its input before it parses, so `new URL("https://exa\nmple.com")` reports on `example.com`. Applying the same deletion to the returned value closes the half of that divergence which can move the host; the parser's other rewrite, stripping C0 controls at the edges, cannot. */
		function stripTabAndNewline(value) {
		    return value.replace(asciiTabOrNewline, "");
		}
		function urlHostnameOk(url, hostname) {
		    hostname.lastIndex = 0;
		    return hostname.test(url.hostname);
		}
		function urlProtocolOk(url, protocol) {
		    protocol.lastIndex = 0;
		    return protocol.test(url.protocol.endsWith(":") ? url.protocol.slice(0, -1) : url.protocol);
		}
		exports.$ZodURL = core.$constructor("$ZodURL", (inst, def) => {
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        try {
		            // Trim whitespace from input
		            const trimmed = payload.value.trim();
		            const url = validateURL(trimmed, def);
		            if (url === exports.URL_BAD_FORMAT) {
		                payload.issues.push({
		                    code: "invalid_format",
		                    format: "url",
		                    note: "Invalid URL format",
		                    input: payload.value,
		                    inst,
		                    continue: !def.abort,
		                });
		                return;
		            }
		            if (url === exports.URL_UNPARSEABLE) {
		                payload.issues.push({
		                    code: "invalid_format",
		                    format: "url",
		                    input: payload.value,
		                    inst,
		                    continue: !def.abort,
		                });
		                return;
		            }
		            if (url === true) {
		                payload.value = stripTabAndNewline(trimmed);
		                return;
		            }
		            if (def.hostname && !urlHostnameOk(url, def.hostname)) {
		                payload.issues.push({
		                    code: "invalid_format",
		                    format: "url",
		                    note: "Invalid hostname",
		                    pattern: def.hostname.source,
		                    input: payload.value,
		                    inst,
		                    continue: !def.abort,
		                });
		            }
		            if (def.protocol && !urlProtocolOk(url, def.protocol)) {
		                payload.issues.push({
		                    code: "invalid_format",
		                    format: "url",
		                    note: "Invalid protocol",
		                    pattern: def.protocol.source,
		                    input: payload.value,
		                    inst,
		                    continue: !def.abort,
		                });
		            }
		            // Set the output value based on normalize flag
		            payload.value = def.normalize ? url.href : stripTabAndNewline(trimmed);
		            return;
		        }
		        catch (_) {
		            payload.issues.push({
		                code: "invalid_format",
		                format: "url",
		                input: payload.value,
		                inst,
		                continue: !def.abort,
		            });
		        }
		    };
		});
		exports.$ZodEmoji = core.$constructor("$ZodEmoji", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.emoji());
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodNanoID = core.$constructor("$ZodNanoID", (inst, def) => {
		    if (def.length !== undefined && (!Number.isInteger(def.length) || def.length < 1))
		        throw new Error(`Invalid nanoid length: ${def.length}`);
		    def.pattern ?? (def.pattern = def.length === undefined ? regexes.nanoid : regexes.nanoidOfLength(def.length));
		    exports.$ZodStringFormat.init(inst, def);
		});
		/**
		 * @deprecated CUID v1 is deprecated by its authors due to information leakage
		 * (timestamps embedded in the id). Use {@link $ZodCUID2} instead.
		 * See https://github.com/paralleldrive/cuid.
		 */
		exports.$ZodCUID = core.$constructor("$ZodCUID", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.cuid);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodCUID2 = core.$constructor("$ZodCUID2", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.cuid2);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodULID = core.$constructor("$ZodULID", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.ulid);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodXID = core.$constructor("$ZodXID", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.xid);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodKSUID = core.$constructor("$ZodKSUID", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.ksuid);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodISODateTime = core.$constructor("$ZodISODateTime", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.datetime(def));
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodISODate = core.$constructor("$ZodISODate", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.date);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodISOTime = core.$constructor("$ZodISOTime", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.time(def));
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodISODuration = core.$constructor("$ZodISODuration", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.duration);
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodIPv4 = core.$constructor("$ZodIPv4", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.ipv4);
		    exports.$ZodStringFormat.init(inst, def);
		});
		/** An IPv6 address is written with hex digits, colons and dots, and nothing else. The guard is what makes the check below an IPv6 check: `new URL("http://[...]")` parses an authority, not an address, so `@` and `\` re-delimit it and `"::@1\\"` validates against the host `0.0.0.1`. The URL parser also deletes ASCII tab, LF and CR rather than failing, which is how `"::1\n"` validated as `::1`. */
		const ipv6Alphabet = /^[0-9a-fA-F:.]+$/;
		function isValidIPv6(value) {
		    if (!ipv6Alphabet.test(value))
		        return false;
		    return canParseURL(`http://[${value}]`);
		}
		exports.$ZodIPv6 = core.$constructor("$ZodIPv6", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.ipv6);
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (!isValidIPv6(payload.value)) {
		            payload.issues.push({
		                code: "invalid_format",
		                format: "ipv6",
		                input: payload.value,
		                inst,
		                continue: !def.abort,
		            });
		        }
		    };
		});
		exports.$ZodMAC = core.$constructor("$ZodMAC", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.mac(def.delimiter));
		    exports.$ZodStringFormat.init(inst, def);
		});
		exports.$ZodCIDRv4 = core.$constructor("$ZodCIDRv4", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.cidrv4);
		    exports.$ZodStringFormat.init(inst, def);
		});
		function isValidCIDRv6(value) {
		    const parts = value.split("/");
		    if (parts.length !== 2)
		        return false;
		    const [address, prefix] = parts;
		    if (!prefix)
		        return false;
		    const prefixNum = Number(prefix);
		    if (`${prefixNum}` !== prefix)
		        return false;
		    if (prefixNum < 0 || prefixNum > 128)
		        return false;
		    return isValidIPv6(address);
		}
		exports.$ZodCIDRv6 = core.$constructor("$ZodCIDRv6", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.cidrv6); // not used for validation
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (!isValidCIDRv6(payload.value)) {
		            payload.issues.push({
		                code: "invalid_format",
		                format: "cidrv6",
		                input: payload.value,
		                inst,
		                continue: !def.abort,
		            });
		        }
		    };
		});
		//////////////////////////////   ZodBase64   //////////////////////////////
		function isValidBase64(data) {
		    if (data === "")
		        return true;
		    // atob ignores whitespace, so reject it up front.
		    if (/\s/.test(data))
		        return false;
		    if (data.length % 4 !== 0)
		        return false;
		    try {
		        // @ts-ignore
		        atob(data);
		        return true;
		    }
		    catch {
		        return false;
		    }
		}
		// lax on purpose: the quantified regexes.base64 overflows the regex stack on multi-MB input and its leading ^$| alternation leaks through template-literal composition; isValidBase64 enforces length and padding
		exports.base64Charset = /^[0-9a-zA-Z+/]*={0,2}$/;
		exports.$ZodBase64 = core.$constructor("$ZodBase64", (inst, def) => {
		    def.pattern ?? (def.pattern = exports.base64Charset);
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (isValidBase64(payload.value))
		            return;
		        payload.issues.push({
		            code: "invalid_format",
		            format: "base64",
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		//////////////////////////////   ZodBase64URL   //////////////////////////////
		// lax on purpose: the quantified regexes.base64url overflows the regex stack on multi-MB input; isValidBase64 enforces length on the padded string
		exports.base64urlCharset = /^[A-Za-z0-9_-]*$/;
		function isValidBase64URL(data) {
		    if (!exports.base64urlCharset.test(data))
		        return false;
		    const base64 = data.replace(/[-_]/g, (c) => (c === "-" ? "+" : "/"));
		    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
		    return isValidBase64(padded);
		}
		exports.$ZodBase64URL = core.$constructor("$ZodBase64URL", (inst, def) => {
		    def.pattern ?? (def.pattern = exports.base64urlCharset);
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (isValidBase64URL(payload.value))
		            return;
		        payload.issues.push({
		            code: "invalid_format",
		            format: "base64url",
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodE164 = core.$constructor("$ZodE164", (inst, def) => {
		    def.pattern ?? (def.pattern = regexes.e164);
		    exports.$ZodStringFormat.init(inst, def);
		});
		//////////////////////////////   ZodCreditCard   //////////////////////////////
		const CC_SANITIZE = /[- ]/g;
		/** Luhn checksum on a digit-only string. Adapted from valibot (MIT). */
		function isLuhnAlgo(digits) {
		    let length = digits.length;
		    let bit = 1;
		    let sum = 0;
		    while (length) {
		        const value = digits.charCodeAt(--length) - 48;
		        bit ^= 1;
		        sum += bit ? [0, 2, 4, 6, 8, 1, 3, 5, 7, 9][value] : value;
		    }
		    return sum % 10 === 0;
		}
		function isValidCreditCard(input) {
		    if (!regexes.creditCard.test(input))
		        return false;
		    return isLuhnAlgo(input.replace(CC_SANITIZE, ""));
		}
		exports.$ZodCreditCard = core.$constructor("$ZodCreditCard", (inst, def) => {
		    // Shape only — the Luhn check below is not expressible as a pattern, so consumers of `pattern` (JSON Schema, template literals) get the length and separator rules alone.
		    def.pattern ?? (def.pattern = regexes.creditCard);
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (isValidCreditCard(payload.value))
		            return;
		        payload.issues.push({
		            code: "invalid_format",
		            format: "credit_card",
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		//////////////////////////////   ZodIBAN   //////////////////////////////
		// iso 7064 mod 97-10 checksum without BigInt
		function isIso7064Mod97(iban) {
		    let remainder = 0;
		    const len = iban.length;
		    for (let i = 4; i < len; i++) {
		        const code = iban.charCodeAt(i);
		        remainder = (code >= 65 ? remainder * 100 + (code - 55) : remainder * 10 + (code - 48)) % 97;
		    }
		    for (let i = 0; i < 4; i++) {
		        const code = iban.charCodeAt(i);
		        remainder = (code >= 65 ? remainder * 100 + (code - 55) : remainder * 10 + (code - 48)) % 97;
		    }
		    return remainder === 1;
		}
		function isValidIBAN(input) {
		    if (!regexes.iban.test(input))
		        return false;
		    return isIso7064Mod97(input);
		}
		exports.$ZodIBAN = core.$constructor("$ZodIBAN", (inst, def) => {
		    // shape only — checksum is not expressible as a pattern
		    def.pattern ?? (def.pattern = regexes.iban);
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (isValidIBAN(payload.value))
		            return;
		        payload.issues.push({
		            code: "invalid_format",
		            format: "iban",
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		//////////////////////////////   ZodJWT   //////////////////////////////
		function isValidJWT(token, algorithm = null) {
		    try {
		        const tokensParts = token.split(".");
		        if (tokensParts.length !== 3)
		            return false;
		        const [header] = tokensParts;
		        if (!header)
		            return false;
		        // @ts-ignore
		        const parsedHeader = JSON.parse(atob(header));
		        if ("typ" in parsedHeader && parsedHeader?.typ !== "JWT")
		            return false;
		        if (!parsedHeader.alg)
		            return false;
		        if (algorithm && (!("alg" in parsedHeader) || parsedHeader.alg !== algorithm))
		            return false;
		        return true;
		    }
		    catch {
		        return false;
		    }
		}
		exports.$ZodJWT = core.$constructor("$ZodJWT", (inst, def) => {
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (isValidJWT(payload.value, def.alg))
		            return;
		        payload.issues.push({
		            code: "invalid_format",
		            format: "jwt",
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodCustomStringFormat = core.$constructor("$ZodCustomStringFormat", (inst, def) => {
		    exports.$ZodStringFormat.init(inst, def);
		    inst._zod.check = (payload) => {
		        if (def.fn(payload.value))
		            return;
		        payload.issues.push({
		            code: "invalid_format",
		            format: def.format,
		            input: payload.value,
		            inst,
		            continue: !def.abort,
		        });
		    };
		});
		exports.$ZodNumber = core.$constructor("$ZodNumber", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.pattern = regexes.number;
		    inst._zod.parse = (payload, _ctx) => {
		        if (def.coerce)
		            try {
		                payload.value = Number(payload.value);
		            }
		            catch (_) { }
		        const input = payload.value;
		        if (typeof input === "number" && !Number.isNaN(input) && Number.isFinite(input)) {
		            return payload;
		        }
		        const received = typeof input === "number"
		            ? Number.isNaN(input)
		                ? "NaN"
		                : !Number.isFinite(input)
		                    ? String(input)
		                    : undefined
		            : undefined;
		        payload.issues.push({
		            expected: "number",
		            code: "invalid_type",
		            input,
		            inst,
		            ...(received ? { received } : {}),
		        });
		        return payload;
		    };
		});
		exports.$ZodNumberFormat = core.$constructor("$ZodNumberFormat", (inst, def) => {
		    checks.$ZodCheckNumberFormat.init(inst, def);
		    exports.$ZodNumber.init(inst, def); // no format checks
		});
		exports.$ZodBoolean = core.$constructor("$ZodBoolean", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.pattern = regexes.boolean;
		    inst._zod.parse = (payload, _ctx) => {
		        if (def.coerce)
		            try {
		                payload.value = Boolean(payload.value);
		            }
		            catch (_) { }
		        const input = payload.value;
		        if (typeof input === "boolean")
		            return payload;
		        payload.issues.push({
		            expected: "boolean",
		            code: "invalid_type",
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodBigInt = core.$constructor("$ZodBigInt", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.pattern = regexes.bigint;
		    inst._zod.parse = (payload, _ctx) => {
		        if (def.coerce)
		            try {
		                payload.value = BigInt(payload.value);
		            }
		            catch (_) { }
		        if (typeof payload.value === "bigint")
		            return payload;
		        payload.issues.push({
		            expected: "bigint",
		            code: "invalid_type",
		            input: payload.value,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodBigIntFormat = core.$constructor("$ZodBigIntFormat", (inst, def) => {
		    checks.$ZodCheckBigIntFormat.init(inst, def);
		    exports.$ZodBigInt.init(inst, def); // no format checks
		});
		exports.$ZodSymbol = core.$constructor("$ZodSymbol", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        if (typeof input === "symbol")
		            return payload;
		        payload.issues.push({
		            expected: "symbol",
		            code: "invalid_type",
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodUndefined = core.$constructor("$ZodUndefined", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.pattern = regexes.undefined;
		    inst._zod.values = new Set([undefined]);
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        if (typeof input === "undefined")
		            return payload;
		        payload.issues.push({
		            expected: "undefined",
		            code: "invalid_type",
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodNull = core.$constructor("$ZodNull", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.pattern = regexes.null;
		    inst._zod.values = new Set([null]);
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        if (input === null)
		            return payload;
		        payload.issues.push({
		            expected: "null",
		            code: "invalid_type",
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodAny = core.$constructor("$ZodAny", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload) => payload;
		});
		exports.$ZodUnknown = core.$constructor("$ZodUnknown", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload) => payload;
		});
		exports.$ZodNever = core.$constructor("$ZodNever", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        payload.issues.push({
		            expected: "never",
		            code: "invalid_type",
		            input: payload.value,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodVoid = core.$constructor("$ZodVoid", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        if (typeof input === "undefined")
		            return payload;
		        payload.issues.push({
		            expected: "void",
		            code: "invalid_type",
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodDate = core.$constructor("$ZodDate", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        if (def.coerce) {
		            try {
		                payload.value = new Date(payload.value);
		            }
		            catch (_err) { }
		        }
		        const input = payload.value;
		        const isDate = input instanceof Date;
		        const isValidDate = isDate && !Number.isNaN(input.getTime());
		        if (isValidDate)
		            return payload;
		        payload.issues.push({
		            expected: "date",
		            code: "invalid_type",
		            input,
		            ...(isDate ? { received: "Invalid Date" } : {}),
		            inst,
		        });
		        return payload;
		    };
		});
		// an oversized container fails its leading size checks before any element is parsed
		function capSize(inst, size) {
		    const caps = util.sizeCaps(inst._zod.def.checks);
		    if (!caps.length)
		        return;
		    const cap = Math.min(...caps.map((c) => c[0]));
		    const parse = inst._zod.parse;
		    inst._zod.parse = (payload, ctx) => {
		        const n = size(payload.value);
		        if (n > cap && !ctx?.skipChecks && ctx?.direction !== "backward") {
		            for (const [max, ch] of util.sizeCaps(inst._zod.def.checks)) {
		                if (n > max && ch._zod.def.when(payload)) {
		                    const start = payload.issues.length;
		                    ch._zod.check(payload);
		                    // the value is still the raw input, so no later check may read it
		                    payload.issues[start].continue = false;
		                    util.attachSchema(payload.issues, start, inst);
		                    return payload;
		                }
		            }
		        }
		        return parse(payload, ctx);
		    };
		}
		const arraySize = (input) => (Array.isArray(input) ? input.length : -1);
		const mapSize = (input) => (input instanceof Map ? input.size : -1);
		const setSize = (input) => (input instanceof Set ? input.size : -1);
		function handleArrayResult(result, final, index) {
		    if (result.issues.length) {
		        final.issues.push(...util.prefixIssues(index, result.issues));
		    }
		    final.value[index] = result.value;
		}
		exports.$ZodArray = core.$constructor("$ZodArray", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const memo = core.globalConfig.memoizer;
		    memo?.attach(inst);
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        if (!Array.isArray(input)) {
		            payload.issues.push({
		                expected: "array",
		                code: "invalid_type",
		                input,
		                inst,
		            });
		            return payload;
		        }
		        payload.value = memo ? memo.alloc(inst, payload, Array(input.length), ctx) : Array(input.length);
		        const proms = [];
		        const abortEarly = ctx?.abortEarly;
		        for (let i = 0; i < input.length; i++) {
		            const item = input[i];
		            const result = def.element._zod.run({
		                value: item,
		                issues: [],
		            }, ctx);
		            if (result instanceof Promise) {
		                proms.push(result.then((result) => handleArrayResult(result, payload, i)));
		            }
		            else {
		                handleArrayResult(result, payload, i);
		                // the element's payload is authoritative here, since handleArrayResult forwards every issue; an object's is not, because it drops a failed absent optional
		                if (abortEarly && result.issues.length !== 0 && util.aborted(result))
		                    break;
		            }
		        }
		        if (proms.length) {
		            return Promise.all(proms).then(() => payload);
		        }
		        return payload; //handleArrayResultsAsync(parseResults, final);
		    };
		    capSize(inst, arraySize);
		});
		function handlePropertyResult(result, final, key, input, optin, optout) {
		    const isPresent = key in input;
		    const isOptionalOut = optout === "optional";
		    // The middle rung means "absence permitted, nothing supplied in its place", so an absent key contributes nothing — whatever the schema made of `undefined` is invented, not substituted. Only `optional` reaches this with a value: `defaulted` substitutes, and a schema that isn't optional-out has to keep the key.
		    if (!isPresent && isOptionalOut && optin === "optional") {
		        return;
		    }
		    if (result.issues.length) {
		        // For optional-in/out schemas, ignore errors on absent keys.
		        if (optin !== undefined && isOptionalOut && !isPresent) {
		            return;
		        }
		        final.issues.push(...util.prefixIssues(key, result.issues));
		    }
		    if (!isPresent && optin === undefined) {
		        if (!result.issues.length) {
		            final.issues.push({
		                code: "invalid_type",
		                expected: "nonoptional",
		                input: undefined,
		                path: [key],
		            });
		        }
		        return;
		    }
		    if (result.value === undefined) {
		        if (isPresent || (optin === "defaulted" && !isOptionalOut)) {
		            final.value[key] = undefined;
		        }
		    }
		    else {
		        final.value[key] = result.value;
		    }
		}
		// one shared instance; a fresh [] per schema cost 56 bytes retained
		const NO_SYMBOL_KEYS = [];
		function normalizeDef(def) {
		    const keys = Object.keys(def.shape);
		    const ownSymbols = Object.getOwnPropertySymbols(def.shape);
		    const symbolKeys = ownSymbols.length ? ownSymbols : NO_SYMBOL_KEYS;
		    // aliases `keys` when there are no symbols, so a string-only shape keeps one array
		    const allKeys = symbolKeys.length ? [...keys, ...symbolKeys] : keys;
		    for (const k of allKeys) {
		        if (!def.shape?.[k]?._zod?.traits?.has("$ZodType")) {
		            throw new Error(`Invalid element at key "${String(k)}": expected a Zod schema`);
		        }
		    }
		    const okeys = util.optionalKeys(def.shape);
		    return {
		        ...def,
		        allKeys,
		        symbolKeys,
		        // string-only: handleCatchall matches it against `for...in`, which never yields a symbol
		        keySet: new Set(keys),
		        numKeys: keys.length,
		        optionalKeys: new Set(okeys),
		    };
		}
		function handleCatchall(proms, input, payload, ctx, def, inst, abortEarly) {
		    const unrecognized = [];
		    const keySet = def.keySet;
		    const _catchall = def.catchall._zod;
		    const t = _catchall.def.type;
		    const optin = _catchall.optin;
		    const optout = _catchall.optout;
		    // starts at 0, not the current length: the shape phase already ran and may have aborted
		    let seen = 0;
		    for (const key in input) {
		        if (abortEarly && payload.issues.length !== seen) {
		            if (util.aborted(payload, seen))
		                break;
		            seen = payload.issues.length;
		        }
		        // Must precede the __proto__ branch: a declared key is not unrecognized, even though the shape loop deliberately strips __proto__ from the parsed output.
		        if (keySet.has(key))
		            continue;
		        // Don't copy an undeclared __proto__ into the result; assignment to a plain {} would replace the result prototype. But in strict mode it is still an unknown key, so report it before skipping.
		        if (key === "__proto__") {
		            if (t === "never")
		                unrecognized.push(key);
		            continue;
		        }
		        if (t === "never") {
		            unrecognized.push(key);
		            continue;
		        }
		        const r = _catchall.run({ value: input[key], issues: [] }, ctx);
		        if (r instanceof Promise) {
		            proms.push(r.then((r) => handlePropertyResult(r, payload, key, input, optin, optout)));
		        }
		        else {
		            handlePropertyResult(r, payload, key, input, optin, optout);
		        }
		    }
		    if (unrecognized.length) {
		        payload.issues.push({
		            code: "unrecognized_keys",
		            keys: unrecognized,
		            input,
		            inst,
		            // Describes the shape of the input, not the validity of the parsed value, so it never aborts. The parse still fails; the schema's own checks just get to run first, and an enclosing intersection can reconcile the key against a sibling operand.
		            continue: true,
		        });
		    }
		    if (!proms.length)
		        return payload;
		    return Promise.all(proms).then(() => {
		        return payload;
		    });
		}
		exports.$ZodObject = core.$constructor("$ZodObject", (inst, def) => {
		    // requires cast because technically $ZodObject doesn't extend
		    exports.$ZodType.init(inst, def);
		    const desc = Object.getOwnPropertyDescriptor(def, "shape");
		    // a cloned def carries its source's accessor, which knows the shape it answers from; adopting that keeps the clone's keys readable without running it
		    const sh = desc?.get ? desc.get.raw : (def.shape ?? {});
		    if (sh) {
		        // Freezes the shape on first read, so its getters resolve once and every later read sees the same schemas.
		        const get = () => {
		            const newSh = { ...sh };
		            Object.defineProperty(def, "shape", { value: newSh });
		            get.raw = newSh;
		            return newSh;
		        };
		        get.raw = sh;
		        Object.defineProperty(def, "shape", { get });
		    }
		    const _normalized = util.cached(() => normalizeDef(def));
		    util.defineLazyInternal(inst, "propValues", (zod) => {
		        const shape = zod.def.shape;
		        const propValues = {};
		        for (const key in shape) {
		            const field = shape[key]._zod;
		            if (field.values) {
		                if (!Object.prototype.hasOwnProperty.call(propValues, key)) {
		                    util.assignProp(propValues, key, new Set());
		                }
		                for (const v of field.values)
		                    propValues[key].add(v);
		                // An omittable slot reads back as undefined at a discriminator lookup, so it has to claim undefined: two options that can both omit the key are not discriminable on it.
		                if (field.optin !== undefined)
		                    propValues[key].add(undefined);
		            }
		        }
		        return propValues;
		    });
		    const isObject = util.isObject;
		    const catchall = def.catchall;
		    let value;
		    const memo = core.globalConfig.memoizer;
		    memo?.attach(inst);
		    inst._zod.parse = (payload, ctx) => {
		        value ?? (value = _normalized.value);
		        const input = payload.value;
		        if (!isObject(input)) {
		            payload.issues.push({
		                expected: "object",
		                code: "invalid_type",
		                input,
		                inst,
		            });
		            return payload;
		        }
		        payload.value = memo ? memo.alloc(inst, payload, {}, ctx) : {};
		        const proms = [];
		        const shape = value.shape;
		        const abortEarly = ctx?.abortEarly;
		        let seen = payload.issues.length;
		        for (const key of value.allKeys) {
		            if (abortEarly && payload.issues.length !== seen) {
		                if (util.aborted(payload, seen))
		                    break;
		                seen = payload.issues.length;
		            }
		            if (key === "__proto__")
		                continue;
		            const el = shape[key];
		            const optin = el._zod.optin;
		            const optout = el._zod.optout;
		            const r = el._zod.run({ value: input[key], issues: [] }, ctx);
		            if (r instanceof Promise) {
		                proms.push(r.then((r) => handlePropertyResult(r, payload, key, input, optin, optout)));
		            }
		            else {
		                handlePropertyResult(r, payload, key, input, optin, optout);
		            }
		        }
		        if (!catchall) {
		            return proms.length ? Promise.all(proms).then(() => payload) : payload;
		        }
		        return handleCatchall(proms, input, payload, ctx, _normalized.value, inst, abortEarly === true);
		    };
		});
		exports.$ZodObjectJIT = core.$constructor("$ZodObjectJIT", (inst, def) => {
		    // requires cast because technically $ZodObject doesn't extend
		    exports.$ZodObject.init(inst, def);
		    const superParse = inst._zod.parse;
		    const _normalized = util.cached(() => normalizeDef(def));
		    const memo = core.globalConfig.memoizer;
		    const generateFastpass = (shape) => {
		        const normalized = _normalized.value;
		        const syms = normalized.symbolKeys;
		        // a symbol has no source literal, so it is read as `syms[i]` off the closed-over scope
		        const doc = new doc_js_1.Doc(["payload", "ctx"], { shape, inst, memo, syms });
		        const parseStr = (k) => `shape[${k}]._zod.run({ value: input[${k}], issues: [] }, ctx)`;
		        // prefixes in place, like util.prefixIssues. newResult must land before the early return: a catchall runs after this and would otherwise write onto the caller's input
		        const prefixStr = (id, k) => `
          let ${id}_ab = false;
          for (let i = 0; i < ${id}.issues.length; i++) {
            const iss = ${id}.issues[i];
            iss.path = iss.path ? [${k}, ...iss.path] : [${k}];
            payload.issues.push(iss);
            if (iss.continue !== true) ${id}_ab = true;
          }
          if (${id}_ab && ctx && ctx.abortEarly) {
            payload.value = newResult;
            return payload;
          }`;
		        doc.write(`const input = payload.value;`);
		        const ids = Object.create(null);
		        let counter = 0;
		        for (const key of normalized.allKeys) {
		            ids[key] = `key_${counter++}`;
		        }
		        // A: preserve key order {
		        doc.write(memo ? `const newResult = memo.alloc(inst, payload, {}, ctx);` : `const newResult = {};`);
		        for (const key of normalized.allKeys) {
		            if (key === "__proto__")
		                continue;
		            const id = ids[key];
		            const k = typeof key === "symbol" ? `syms[${syms.indexOf(key)}]` : util.esc(key);
		            const isPresent = `${k} in input`;
		            const schema = shape[key];
		            const optin = schema?._zod?.optin;
		            const isOptionalIn = optin !== undefined;
		            const isOptionalOut = schema?._zod?.optout === "optional";
		            doc.write(`const ${id} = ${parseStr(k)};`);
		            if (isOptionalIn && isOptionalOut) {
		                // For optional-in/out schemas, ignore errors on absent keys — and, like the interpreted path, drop the value produced alongside them. The middle rung goes further: it permits absence without supplying anything in its place, so an absent key contributes nothing at all.
		                const assign = optin === "optional" ? `${id}_present` : `${id}.value !== undefined || ${id}_present`;
		                doc.write(`
        const ${id}_present = ${isPresent};
        if (!${id}.issues.length || ${id}_present) {
          if (${id}.issues.length) {${prefixStr(id, k)}
          }

          if (${assign}) {
            newResult[${k}] = ${id}.value;
          }
        }

      `);
		            }
		            else if (!isOptionalIn) {
		                doc.write(`
        const ${id}_present = ${isPresent};
        if (${id}.issues.length) {${prefixStr(id, k)}
        }
        if (!${id}_present && !${id}.issues.length) {
          payload.issues.push({
            code: "invalid_type",
            expected: "nonoptional",
            input: undefined,
            path: [${k}]
          });
          if (ctx && ctx.abortEarly) {
            payload.value = newResult;
            return payload;
          }
        }

        if (${id}_present) {
          newResult[${k}] = ${id}.value;
        }

      `);
		            }
		            else {
		                doc.write(`
        if (${id}.issues.length) {${prefixStr(id, k)}
        }
      `);
		                if (optin === "defaulted") {
		                    doc.write(`newResult[${k}] = ${id}.value;`);
		                }
		                else {
		                    doc.write(`
        if (${id}.value !== undefined || ${isPresent}) {
          newResult[${k}] = ${id}.value;
        }
      `);
		                }
		            }
		        }
		        doc.write(`payload.value = newResult;`);
		        doc.write(`return payload;`);
		        // closing `shape` in is what pays: turbofan specializes the parser against that one shape object, so every `shape[k]._zod.run` folds to a known callee. as a parameter it stays a generic load and measures 13% slower even with the forwarding frame gone
		        return doc.compile();
		    };
		    let fastpass;
		    const isObject = util.isObject;
		    const jit = !core.globalConfig.jitless;
		    const allowsEval = util.allowsEval;
		    const fastEnabled = jit && allowsEval.value; // && !def.catchall;
		    const catchall = def.catchall;
		    let value;
		    inst._zod.parse = (payload, ctx) => {
		        value ?? (value = _normalized.value);
		        const input = payload.value;
		        if (!isObject(input)) {
		            payload.issues.push({
		                expected: "object",
		                code: "invalid_type",
		                input,
		                inst,
		            });
		            return payload;
		        }
		        if (jit && fastEnabled && ctx?.async === false && ctx.jitless !== true) {
		            // always synchronous
		            if (!fastpass)
		                fastpass = generateFastpass(def.shape);
		            payload = fastpass(payload, ctx);
		            if (!catchall)
		                return payload;
		            return handleCatchall([], input, payload, ctx, value, inst, ctx?.abortEarly === true);
		        }
		        return superParse(payload, ctx);
		    };
		});
		function handleUnionResults(results, final, inst, ctx) {
		    for (const result of results) {
		        if (result.issues.length === 0) {
		            final.value = result.value;
		            return final;
		        }
		    }
		    const nonaborted = results.filter((r) => !util.aborted(r));
		    if (nonaborted.length === 1) {
		        final.value = nonaborted[0].value;
		        return nonaborted[0];
		    }
		    final.issues.push({
		        code: "invalid_union",
		        input: final.value,
		        inst,
		        errors: results.map((result) => result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config()))),
		    });
		    return final;
		}
		exports.$ZodUnion = core.$constructor("$ZodUnion", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.options.some((o) => o._zod.optin === "defaulted")
		        ? "defaulted"
		        : zod.def.options.some((o) => o._zod.optin !== undefined)
		            ? "optional"
		            : undefined);
		    util.defineLazyInternal(inst, "optout", (zod) => zod.def.options.some((o) => o._zod.optout === "optional") ? "optional" : undefined);
		    util.defineLazyInternal(inst, "values", (zod) => {
		        if (zod.def.options.every((o) => o._zod.values)) {
		            return new Set(zod.def.options.flatMap((option) => Array.from(option._zod.values)));
		        }
		        return undefined;
		    });
		    util.defineLazyInternal(inst, "pattern", (zod) => {
		        if (zod.def.options.every((o) => o._zod.pattern)) {
		            const patterns = zod.def.options.map((o) => o._zod.pattern);
		            return new RegExp(`^(${patterns.map((p) => util.cleanRegex(p.source)).join("|")})$`);
		        }
		        return undefined;
		    });
		    const first = def.options.length === 1 ? def.options[0]._zod.run : null;
		    inst._zod.parse = (payload, ctx) => {
		        if (first) {
		            return first(payload, ctx);
		        }
		        let async = false;
		        const results = [];
		        for (const option of def.options) {
		            const result = option._zod.run({
		                value: payload.value,
		                issues: [],
		            }, ctx);
		            if (result instanceof Promise) {
		                results.push(result);
		                async = true;
		            }
		            else {
		                if (result.issues.length === 0)
		                    return result;
		                results.push(result);
		            }
		        }
		        if (!async)
		            return handleUnionResults(results, payload, inst, ctx);
		        return Promise.all(results).then((results) => {
		            return handleUnionResults(results, payload, inst, ctx);
		        });
		    };
		});
		function handleExclusiveUnionResults(results, final, inst, ctx) {
		    const matches = [];
		    for (let i = 0; i < results.length; i++) {
		        if (results[i].issues.length === 0)
		            matches.push(i);
		    }
		    if (matches.length === 1) {
		        final.value = results[matches[0]].value;
		        return final;
		    }
		    if (matches.length === 0) {
		        // No matches - same as regular union
		        final.issues.push({
		            code: "invalid_union",
		            input: final.value,
		            inst,
		            errors: results.map((result) => result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config()))),
		        });
		    }
		    else {
		        // Multiple matches - exclusive union failure
		        final.issues.push({
		            code: "invalid_union",
		            input: final.value,
		            inst,
		            errors: [],
		            inclusive: false,
		            matches,
		        });
		    }
		    return final;
		}
		exports.$ZodXor = core.$constructor("$ZodXor", (inst, def) => {
		    exports.$ZodUnion.init(inst, def);
		    def.inclusive = false;
		    const first = def.options.length === 1 ? def.options[0]._zod.run : null;
		    inst._zod.parse = (payload, ctx) => {
		        if (first) {
		            return first(payload, ctx);
		        }
		        let async = false;
		        const results = [];
		        for (const option of def.options) {
		            const result = option._zod.run({
		                value: payload.value,
		                issues: [],
		            }, ctx);
		            if (result instanceof Promise) {
		                results.push(result);
		                async = true;
		            }
		            else {
		                results.push(result);
		            }
		        }
		        if (!async)
		            return handleExclusiveUnionResults(results, payload, inst, ctx);
		        return Promise.all(results).then((results) => {
		            return handleExclusiveUnionResults(results, payload, inst, ctx);
		        });
		    };
		});
		/** Returns the option whose discriminator claims `value`, or throws if ambiguous. */
		function getDiscriminatedOption(union, value) {
		    const internals = union._zod;
		    let map = internals.bag.optionsMap;
		    if (!map) {
		        map = discriminatorMap(internals.def);
		        internals.bag.optionsMap = map;
		    }
		    const option = map.get(value);
		    if (option === null)
		        throw new Error(`Ambiguous discriminator value "${String(value)}"`);
		    return option;
		}
		function discriminatorMap(def) {
		    const map = new Map();
		    for (const option of def.options) {
		        const values = option._zod.propValues?.[def.discriminator];
		        if (!values || values.size === 0)
		            throw new Error(`Invalid discriminated union option at index "${def.options.indexOf(option)}"`);
		        for (const value of values) {
		            if (map.has(value)) {
		                if (value !== undefined)
		                    throw new Error(`Duplicate discriminator value "${String(value)}"`);
		                // keep the collision marked so a later member cannot reclaim it
		                map.set(value, null);
		            }
		            else {
		                map.set(value, option);
		            }
		        }
		    }
		    return map;
		}
		exports.$ZodDiscriminatedUnion = 
		/*@__PURE__*/
		core.$constructor("$ZodDiscriminatedUnion", (inst, def) => {
		    def.inclusive = false;
		    exports.$ZodUnion.init(inst, def);
		    const _super = inst._zod.parse;
		    util.defineLazyInternal(inst, "propValues", (zod) => {
		        const propValues = {};
		        let undefinedCount = 0;
		        for (const option of zod.def.options) {
		            const pv = option._zod.propValues;
		            if (!pv || Object.keys(pv).length === 0)
		                throw new Error(`Invalid discriminated union option at index "${zod.def.options.indexOf(option)}"`);
		            if (pv[zod.def.discriminator]?.has(undefined))
		                undefinedCount++;
		            for (const [k, v] of Object.entries(pv)) {
		                if (!Object.prototype.hasOwnProperty.call(propValues, k)) {
		                    util.assignProp(propValues, k, new Set());
		                }
		                for (const val of v) {
		                    propValues[k].add(val);
		                }
		            }
		        }
		        if (!zod.def.unionFallback && undefinedCount > 1)
		            propValues[zod.def.discriminator]?.delete(undefined);
		        return propValues;
		    });
		    // Checked now rather than in the lookup map below, so an option that lacks the discriminator fails at the `discriminatedUnion` call instead of on the first object parsed. Options whose shape cannot be enumerated without resolving it — pipes and lazies — are left to the map.
		    def.options.forEach((option, i) => {
		        const propShape = util.rawShape(option._zod.def);
		        if (propShape && !Object.prototype.hasOwnProperty.call(propShape, def.discriminator)) {
		            throw new Error(`Invalid discriminated union option at index "${i}"`);
		        }
		    });
		    const disc = util.cached(() => discriminatorMap(def));
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        if (!util.isObject(input)) {
		            payload.issues.push({
		                code: "invalid_type",
		                expected: "object",
		                input,
		                inst,
		            });
		            return payload;
		        }
		        const value = input?.[def.discriminator];
		        const opt = disc.value.get(value);
		        // forward metadata cannot choose an encoder for an absent tag
		        if (opt && (value !== undefined || ctx.direction !== "backward")) {
		            return opt._zod.run(payload, ctx);
		        }
		        // Fall back to union matching when the fast discriminator path fails:
		        // - explicitly enabled via unionFallback, or
		        // - during backward direction (encode), since codec-based discriminators have different values in forward vs backward directions
		        if (def.unionFallback || ctx.direction === "backward") {
		            return _super(payload, ctx);
		        }
		        // no matching discriminator
		        payload.issues.push({
		            code: "invalid_union",
		            errors: [],
		            note: "No matching discriminator",
		            discriminator: def.discriminator,
		            options: Array.from(disc.value.keys()).filter((value) => disc.value.get(value) !== null),
		            input,
		            path: [def.discriminator],
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodIntersection = core.$constructor("$ZodIntersection", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        const left = def.left._zod.run({ value: input, issues: [] }, ctx);
		        const right = def.right._zod.run({ value: input, issues: [] }, ctx);
		        const async = left instanceof Promise || right instanceof Promise;
		        if (async) {
		            return Promise.all([left, right]).then(([left, right]) => {
		                return handleIntersectionResults(payload, left, right);
		            });
		        }
		        return handleIntersectionResults(payload, left, right);
		    };
		});
		function mergeValues(a, b) {
		    // const aType = parse.t(a);
		    // const bType = parse.t(b);
		    if (a === b) {
		        return { valid: true, data: a };
		    }
		    if (a instanceof Date && b instanceof Date && +a === +b) {
		        return { valid: true, data: a };
		    }
		    if (util.isPlainObject(a) && util.isPlainObject(b)) {
		        const bKeys = Object.keys(b);
		        const sharedKeys = Object.keys(a).filter((key) => bKeys.indexOf(key) !== -1);
		        const newObj = { ...a, ...b };
		        if (Object.prototype.hasOwnProperty.call(newObj, "__proto__"))
		            delete newObj.__proto__;
		        for (const key of sharedKeys) {
		            if (key === "__proto__")
		                continue;
		            const sharedValue = mergeValues(a[key], b[key]);
		            if (!sharedValue.valid) {
		                return {
		                    valid: false,
		                    mergeErrorPath: [key, ...sharedValue.mergeErrorPath],
		                };
		            }
		            newObj[key] = sharedValue.data;
		        }
		        return { valid: true, data: newObj };
		    }
		    if (Array.isArray(a) && Array.isArray(b)) {
		        if (a.length !== b.length) {
		            return { valid: false, mergeErrorPath: [] };
		        }
		        const newArray = [];
		        for (let index = 0; index < a.length; index++) {
		            const itemA = a[index];
		            const itemB = b[index];
		            const sharedValue = mergeValues(itemA, itemB);
		            if (!sharedValue.valid) {
		                return {
		                    valid: false,
		                    mergeErrorPath: [index, ...sharedValue.mergeErrorPath],
		                };
		            }
		            newArray.push(sharedValue.data);
		        }
		        return { valid: true, data: newArray };
		    }
		    return { valid: false, mergeErrorPath: [] };
		}
		function handleIntersectionResults(result, left, right) {
		    // Track which side(s) reject each key. A key rejection is reported only when BOTH sides reject it, so a key owned by one branch survives the other's key schema. strictObject reports these as unrecognized_keys; a record with an open key schema reports one invalid_key per key.
		    const unrecKeys = new Map();
		    let unrecIssue;
		    const keyIssues = new Map();
		    const collect = (iss, side) => {
		        let keys;
		        if (iss.code === "unrecognized_keys" && !iss.path?.length) {
		            unrecIssue ?? (unrecIssue = iss);
		            keys = iss.keys;
		        }
		        else if (iss.code === "invalid_key" && iss.origin === "record" && iss.path?.length === 1) {
		            const k = String(iss.path[0]);
		            if (!keyIssues.has(k))
		                keyIssues.set(k, iss);
		            keys = [k];
		        }
		        else {
		            return false;
		        }
		        for (const k of keys) {
		            if (!unrecKeys.has(k))
		                unrecKeys.set(k, {});
		            unrecKeys.get(k)[side] = true;
		        }
		        return true;
		    };
		    for (const iss of left.issues) {
		        if (!collect(iss, "l"))
		            result.issues.push(iss);
		    }
		    for (const iss of right.issues) {
		        if (!collect(iss, "r"))
		            result.issues.push(iss);
		    }
		    // Report only keys rejected by BOTH sides
		    const bothKeys = [...unrecKeys].filter(([, f]) => f.l && f.r).map(([k]) => k);
		    if (bothKeys.length) {
		        const aggregated = unrecIssue ? bothKeys.filter((k) => unrecIssue.keys.includes(k)) : [];
		        if (aggregated.length)
		            result.issues.push({ ...unrecIssue, keys: aggregated });
		        for (const k of bothKeys) {
		            if (!aggregated.includes(k) && keyIssues.has(k))
		                result.issues.push(keyIssues.get(k));
		        }
		    }
		    const merged = mergeValues(left.value, right.value);
		    if (!merged.valid) {
		        if (util.aborted(result))
		            return result;
		        throw new Error(`Unmergable intersection. Error path: ` + `${JSON.stringify(merged.mergeErrorPath)}`);
		    }
		    result.value = merged.data;
		    return result;
		}
		exports.$ZodTuple = core.$constructor("$ZodTuple", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const items = def.items;
		    const memo = core.globalConfig.memoizer;
		    memo?.attach(inst);
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        if (!Array.isArray(input)) {
		            payload.issues.push({
		                input,
		                inst,
		                expected: "tuple",
		                code: "invalid_type",
		            });
		            return payload;
		        }
		        payload.value = memo ? memo.alloc(inst, payload, [], ctx) : [];
		        const proms = [];
		        const optinStart = getTupleOptStart(items, "optin");
		        const optoutStart = getTupleOptStart(items, "optout");
		        if (!def.rest) {
		            if (input.length < optinStart) {
		                payload.issues.push({
		                    code: "too_small",
		                    minimum: optinStart,
		                    inclusive: true,
		                    input,
		                    inst,
		                    origin: "array",
		                });
		                return payload;
		            }
		            if (input.length > items.length) {
		                payload.issues.push({
		                    code: "too_big",
		                    maximum: items.length,
		                    inclusive: true,
		                    input,
		                    inst,
		                    origin: "array",
		                });
		            }
		        }
		        // Run every item in parallel, collecting results into an indexed array. The post-processing in `handleTupleResults` walks them in order so it can decide whether an absent optional-output error can truncate the tail or must be reported to preserve required output.
		        const itemResults = new Array(items.length);
		        // only tracked when there is a rest loop to skip
		        const abortEarly = def.rest ? ctx?.abortEarly : undefined;
		        let itemAborted = false;
		        for (let i = 0; i < items.length; i++) {
		            const r = items[i]._zod.run({ value: input[i], issues: [] }, ctx);
		            if (r instanceof Promise) {
		                proms.push(r.then((rr) => {
		                    itemResults[i] = rr;
		                }));
		            }
		            else {
		                itemResults[i] = r;
		                if (abortEarly && !itemAborted && r.issues.length)
		                    itemAborted = util.aborted(r);
		            }
		        }
		        // sound because rest is non-empty exactly when every fixed index is present, the one case handleTupleResults cannot discard an item's issues
		        if (def.rest && !itemAborted) {
		            let i = items.length - 1;
		            const rest = input.slice(items.length);
		            let seen = payload.issues.length;
		            for (const el of rest) {
		                if (abortEarly && payload.issues.length !== seen) {
		                    if (util.aborted(payload, seen))
		                        break;
		                    seen = payload.issues.length;
		                }
		                i++;
		                const result = def.rest._zod.run({ value: el, issues: [] }, ctx);
		                if (result instanceof Promise) {
		                    proms.push(result.then((r) => handleTupleResult(r, payload, i)));
		                }
		                else {
		                    handleTupleResult(result, payload, i);
		                }
		            }
		        }
		        if (proms.length) {
		            return Promise.all(proms).then(() => handleTupleResults(itemResults, payload, items, input, optoutStart));
		        }
		        return handleTupleResults(itemResults, payload, items, input, optoutStart);
		    };
		});
		function getTupleOptStart(items, key) {
		    for (let i = items.length - 1; i >= 0; i--) {
		        // optin is a three-rung ladder so any rung above `undefined` permits an absent slot; optout stays two-valued.
		        const omittable = key === "optin" ? items[i]._zod.optin !== undefined : items[i]._zod.optout === "optional";
		        if (!omittable)
		            return i + 1;
		    }
		    return 0;
		}
		function handleTupleResult(result, final, index) {
		    if (result.issues.length) {
		        final.issues.push(...util.prefixIssues(index, result.issues));
		    }
		    final.value[index] = result.value;
		}
		function handleTupleResults(itemResults, final, items, input, optoutStart) {
		    // Walk results in order. Mirror $ZodObject's swallow-on-absent-optional rule, but only after `optoutStart`: the first index where the output tuple tail can be absent.
		    for (let i = 0; i < items.length; i++) {
		        const r = itemResults[i];
		        const isPresent = i < input.length;
		        // The array analog of `handlePropertyResult`'s absent-key early return: the middle rung permits absence without supplying anything in its place, so the tail truncates here instead of materializing whatever the item made of `undefined`.
		        if (!isPresent && i >= optoutStart && items[i]._zod.optin === "optional") {
		            final.value.length = i;
		            break;
		        }
		        if (r.issues.length) {
		            if (!isPresent && i >= optoutStart) {
		                final.value.length = i;
		                break;
		            }
		            final.issues.push(...util.prefixIssues(i, r.issues));
		        }
		        final.value[i] = r.value;
		    }
		    // Drop trailing slots that produced `undefined` for absent input
		    // (the array analog of an absent optional key on an object). The
		    // `i >= input.length` floor is critical: an explicit `undefined`
		    // *inside* the input must be preserved even when the schema is
		    // optional-out (e.g. `z.string().or(z.undefined())` accepting an
		    // explicit undefined value).
		    for (let i = final.value.length - 1; i >= input.length; i--) {
		        if (items[i]._zod.optout === "optional" && final.value[i] === undefined) {
		            final.value.length = i;
		        }
		        else {
		            break;
		        }
		    }
		    return final;
		}
		exports.$ZodRecord = core.$constructor("$ZodRecord", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const memo = core.globalConfig.memoizer;
		    memo?.attach(inst);
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        if (!util.isPlainObject(input)) {
		            payload.issues.push({
		                expected: "record",
		                code: "invalid_type",
		                input,
		                inst,
		            });
		            return payload;
		        }
		        // no guard in either loop below: a record's invalid_key aborts but an enclosing intersection can reconcile it, so a stopped loop hides keys the sibling does not own and the intersection then rejects nothing
		        const proms = [];
		        const values = def.keyType._zod.values;
		        if (values && !def.partial) {
		            payload.value = memo ? memo.alloc(inst, payload, {}, ctx) : {};
		            const recordKeys = new Set();
		            for (const key of values) {
		                if (typeof key === "string" || typeof key === "number" || typeof key === "symbol") {
		                    recordKeys.add(typeof key === "number" ? key.toString() : key);
		                    // A declared __proto__ is stripped but is not an unrecognized key.
		                    if (key === "__proto__")
		                        continue;
		                    const keyResult = def.keyType._zod.run({ value: key, issues: [] }, ctx);
		                    if (keyResult instanceof Promise) {
		                        throw new Error("Async schemas not supported in object keys currently");
		                    }
		                    if (keyResult.issues.length) {
		                        payload.issues.push({
		                            code: "invalid_key",
		                            origin: "record",
		                            issues: keyResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
		                            input: key,
		                            path: [key],
		                            inst,
		                        });
		                        continue;
		                    }
		                    const outKey = keyResult.value;
		                    if (outKey === "__proto__")
		                        continue;
		                    const result = def.valueType._zod.run({ value: input[key], issues: [] }, ctx);
		                    if (result instanceof Promise) {
		                        proms.push(result.then((result) => {
		                            if (result.issues.length) {
		                                payload.issues.push(...util.prefixIssues(key, result.issues));
		                            }
		                            payload.value[outKey] = result.value;
		                        }));
		                    }
		                    else {
		                        if (result.issues.length) {
		                            payload.issues.push(...util.prefixIssues(key, result.issues));
		                        }
		                        payload.value[outKey] = result.value;
		                    }
		                }
		            }
		            let unrecognized;
		            for (const key in input) {
		                if (!recordKeys.has(key)) {
		                    if (def.mode === "loose") {
		                        // skip __proto__ so it can't replace the result prototype via the assignment setter on the plain {} we build into
		                        if (key === "__proto__")
		                            continue;
		                        payload.value[key] = input[key];
		                    }
		                    else {
		                        unrecognized = unrecognized ?? [];
		                        unrecognized.push(key);
		                    }
		                }
		            }
		            if (unrecognized && unrecognized.length > 0) {
		                payload.issues.push({
		                    code: "unrecognized_keys",
		                    input,
		                    inst,
		                    keys: unrecognized,
		                    continue: true,
		                });
		            }
		        }
		        else {
		            payload.value = memo ? memo.alloc(inst, payload, {}, ctx) : {};
		            // An enumerable key schema declares which keys the record owns, so a key outside the set is unrecognized. A non-enumerable one (regex, refine) is a constraint every key must satisfy, so a failing key is invalid. Only the former is reconcilable against the other side of an intersection.
		            let unrecognized;
		            // Reflect.ownKeys for Symbol-key support; filter non-enumerable to match z.object()
		            for (const key of Reflect.ownKeys(input)) {
		                if (key === "__proto__")
		                    continue;
		                if (!Object.prototype.propertyIsEnumerable.call(input, key))
		                    continue;
		                let keyResult = def.keyType._zod.run({ value: key, issues: [] }, ctx);
		                if (keyResult instanceof Promise) {
		                    throw new Error("Async schemas not supported in object keys currently");
		                }
		                // Numeric string fallback: if key is a numeric string and failed, retry with Number(key). This handles z.number(), z.literal([1, 2, 3]), and unions containing numeric literals
		                const checkNumericKey = typeof key === "string" && regexes.number.test(key) && keyResult.issues.length;
		                if (checkNumericKey) {
		                    const retryResult = def.keyType._zod.run({ value: Number(key), issues: [] }, ctx);
		                    if (retryResult instanceof Promise) {
		                        throw new Error("Async schemas not supported in object keys currently");
		                    }
		                    if (retryResult.issues.length === 0) {
		                        keyResult = retryResult;
		                    }
		                }
		                if (keyResult.issues.length) {
		                    if (def.mode === "loose") {
		                        // Pass through unchanged
		                        payload.value[key] = input[key];
		                    }
		                    else if (values) {
		                        unrecognized = unrecognized ?? [];
		                        unrecognized.push(key);
		                    }
		                    else {
		                        // Default "strict" behavior: error on invalid key
		                        payload.issues.push({
		                            code: "invalid_key",
		                            origin: "record",
		                            issues: keyResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
		                            input: key,
		                            path: [key],
		                            inst,
		                        });
		                    }
		                    continue;
		                }
		                // the guard above tests the raw input key, but the key schema can normalize an ordinary key into __proto__; re-check the key we actually write under
		                const outKey = keyResult.value;
		                if (outKey === "__proto__")
		                    continue;
		                const result = def.valueType._zod.run({ value: input[key], issues: [] }, ctx);
		                if (result instanceof Promise) {
		                    proms.push(result.then((result) => {
		                        if (result.issues.length) {
		                            payload.issues.push(...util.prefixIssues(key, result.issues));
		                        }
		                        payload.value[outKey] = result.value;
		                    }));
		                }
		                else {
		                    if (result.issues.length) {
		                        payload.issues.push(...util.prefixIssues(key, result.issues));
		                    }
		                    payload.value[outKey] = result.value;
		                }
		            }
		            if (unrecognized && unrecognized.length > 0) {
		                payload.issues.push({
		                    code: "unrecognized_keys",
		                    input,
		                    inst,
		                    keys: unrecognized,
		                    continue: true,
		                });
		            }
		        }
		        if (proms.length) {
		            return Promise.all(proms).then(() => payload);
		        }
		        return payload;
		    };
		});
		exports.$ZodMap = core.$constructor("$ZodMap", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const memo = core.globalConfig.memoizer;
		    memo?.attach(inst);
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        if (!(input instanceof Map)) {
		            payload.issues.push({
		                expected: "map",
		                code: "invalid_type",
		                input,
		                inst,
		            });
		            return payload;
		        }
		        const proms = [];
		        payload.value = memo ? memo.alloc(inst, payload, new Map(), ctx) : new Map();
		        const abortEarly = ctx?.abortEarly;
		        let seen = payload.issues.length;
		        for (const [key, value] of input) {
		            if (abortEarly && payload.issues.length !== seen) {
		                if (util.aborted(payload, seen))
		                    break;
		                seen = payload.issues.length;
		            }
		            const keyResult = def.keyType._zod.run({ value: key, issues: [] }, ctx);
		            const valueResult = def.valueType._zod.run({ value: value, issues: [] }, ctx);
		            if (keyResult instanceof Promise || valueResult instanceof Promise) {
		                proms.push(Promise.all([keyResult, valueResult]).then(([keyResult, valueResult]) => {
		                    handleMapResult(keyResult, valueResult, payload, key, input, inst, ctx);
		                }));
		            }
		            else {
		                handleMapResult(keyResult, valueResult, payload, key, input, inst, ctx);
		            }
		        }
		        if (proms.length)
		            return Promise.all(proms).then(() => payload);
		        return payload;
		    };
		    capSize(inst, mapSize);
		});
		function handleMapResult(keyResult, valueResult, final, key, input, inst, ctx) {
		    if (keyResult.issues.length) {
		        if (util.propertyKeyTypes.has(typeof key)) {
		            final.issues.push(...util.prefixIssues(key, keyResult.issues));
		        }
		        else {
		            final.issues.push({
		                code: "invalid_key",
		                origin: "map",
		                input,
		                inst,
		                issues: keyResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
		            });
		        }
		    }
		    if (valueResult.issues.length) {
		        if (util.propertyKeyTypes.has(typeof key)) {
		            final.issues.push(...util.prefixIssues(key, valueResult.issues));
		        }
		        else {
		            final.issues.push({
		                origin: "map",
		                code: "invalid_element",
		                input,
		                inst,
		                key: key,
		                issues: valueResult.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
		            });
		        }
		    }
		    final.value.set(keyResult.value, valueResult.value);
		}
		exports.$ZodSet = core.$constructor("$ZodSet", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const memo = core.globalConfig.memoizer;
		    memo?.attach(inst);
		    inst._zod.parse = (payload, ctx) => {
		        const input = payload.value;
		        if (!(input instanceof Set)) {
		            payload.issues.push({
		                input,
		                inst,
		                expected: "set",
		                code: "invalid_type",
		            });
		            return payload;
		        }
		        const proms = [];
		        payload.value = memo ? memo.alloc(inst, payload, new Set(), ctx) : new Set();
		        const abortEarly = ctx?.abortEarly;
		        let seen = payload.issues.length;
		        for (const item of input) {
		            if (abortEarly && payload.issues.length !== seen) {
		                if (util.aborted(payload, seen))
		                    break;
		                seen = payload.issues.length;
		            }
		            const result = def.valueType._zod.run({ value: item, issues: [] }, ctx);
		            if (result instanceof Promise) {
		                proms.push(result.then((result) => handleSetResult(result, payload)));
		            }
		            else
		                handleSetResult(result, payload);
		        }
		        if (proms.length)
		            return Promise.all(proms).then(() => payload);
		        return payload;
		    };
		    capSize(inst, setSize);
		});
		function handleSetResult(result, final) {
		    if (result.issues.length) {
		        final.issues.push(...result.issues);
		    }
		    final.value.add(result.value);
		}
		exports.$ZodEnum = core.$constructor("$ZodEnum", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const values = util.getEnumValues(def.entries);
		    const valuesSet = new Set(values);
		    inst._zod.values = valuesSet;
		    util.defineLazyInternal(inst, "pattern", (zod) => {
		        const patternValues = util.getEnumValues(zod.def.entries).filter((k) => util.propertyKeyTypes.has(typeof k));
		        // unmatchable fallback, RE2-safe: an empty alternation would compile to /^()$/, which matches ""
		        return new RegExp(patternValues.length ? `^(${patternValues.map((o) => util.escapeRegex(o.toString())).join("|")})$` : "^[^\\s\\S]$");
		    });
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        if (valuesSet.has(input)) {
		            return payload;
		        }
		        payload.issues.push({
		            code: "invalid_value",
		            values,
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodLiteral = core.$constructor("$ZodLiteral", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const values = new Set(def.values);
		    inst._zod.values = values;
		    util.defineLazyInternal(inst, "pattern", (zod) => {
		        const vals = zod.def.values;
		        // unmatchable fallback, RE2-safe: an empty alternation would compile to /^()$/, which matches ""
		        return new RegExp(vals.length
		            ? `^(${vals
		                .map((o) => typeof o === "string" ? util.escapeRegex(o) : o ? util.escapeRegex(o.toString()) : String(o))
		                .join("|")})$`
		            : "^[^\\s\\S]$");
		    });
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        if (values.has(input)) {
		            return payload;
		        }
		        payload.issues.push({
		            code: "invalid_value",
		            values: def.values,
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodFile = core.$constructor("$ZodFile", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        const input = payload.value;
		        // @ts-ignore
		        if (input instanceof File)
		            return payload;
		        payload.issues.push({
		            expected: "file",
		            code: "invalid_type",
		            input,
		            inst,
		        });
		        return payload;
		    };
		});
		exports.$ZodTransform = core.$constructor("$ZodTransform", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.optin = "optional";
		    core.globalConfig.memoizer?.guard(inst);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            throw new core.$ZodEncodeError(inst.constructor.name);
		        }
		        const _out = def.transform(payload.value, payload);
		        if (ctx.async) {
		            const output = _out instanceof Promise ? _out : Promise.resolve(_out);
		            return output.then((output) => {
		                payload.value = output;
		                return payload;
		            });
		        }
		        if (_out instanceof Promise) {
		            throw new core.$ZodAsyncError();
		        }
		        payload.value = _out;
		        return payload;
		    };
		});
		function handleOptionalResult(payload, result) {
		    // A substituting schema that still failed has no usable answer; yield undefined. Its issues are simply dropped: it ran on a payload of its own, so there is no shared array to truncate and nothing of the caller's to lose with it.
		    payload.value = result.issues.length ? undefined : result.value;
		    return payload;
		}
		exports.$ZodOptional = core.$constructor("$ZodOptional", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    // .optional() propagates absence rather than substituting for it, so a defaulted inner keeps its rung.
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.innerType._zod.optin === "defaulted" ? "defaulted" : "optional");
		    inst._zod.optout = "optional";
		    util.defineLazyInternal(inst, "values", (zod) => {
		        const values = zod.def.innerType._zod.values;
		        return values ? new Set([...values, undefined]) : undefined;
		    });
		    util.defineLazyInternal(inst, "pattern", (zod) => {
		        const pattern = zod.def.innerType._zod.pattern;
		        return pattern ? new RegExp(`^(${util.cleanRegex(pattern.source)})?$`) : undefined;
		    });
		    inst._zod.parse = (payload, ctx) => {
		        if (payload.value === undefined) {
		            // Only the top rung substitutes a value for absence; everything else leaves it intact, which is what .optional() means.
		            if (def.innerType._zod.optin !== "defaulted")
		                return payload;
		            // Its own payload, for the same reason $ZodCatch gets one: a pipe forwards an unrecognized key through the caller's issues array, and this must not read that as the substituting schema failing and drop it.
		            const result = def.innerType._zod.run({ value: payload.value, issues: [] }, ctx);
		            if (result instanceof Promise)
		                return result.then((result) => handleOptionalResult(payload, result));
		            return handleOptionalResult(payload, result);
		        }
		        return def.innerType._zod.run(payload, ctx);
		    };
		});
		exports.$ZodExactOptional = core.$constructor("$ZodExactOptional", (inst, def) => {
		    // Call parent init - inherits optin/optout = "optional"
		    exports.$ZodOptional.init(inst, def);
		    // Override values/pattern to NOT add undefined
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.innerType._zod.values);
		    util.defineLazyInternal(inst, "pattern", (zod) => zod.def.innerType._zod.pattern);
		    // Override parse to just delegate (no undefined handling)
		    inst._zod.parse = (payload, ctx) => {
		        return def.innerType._zod.run(payload, ctx);
		    };
		});
		exports.$ZodNullable = core.$constructor("$ZodNullable", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.innerType._zod.optin);
		    util.defineLazyInternal(inst, "optout", (zod) => zod.def.innerType._zod.optout);
		    util.defineLazyInternal(inst, "pattern", (zod) => {
		        const pattern = zod.def.innerType._zod.pattern;
		        return pattern ? new RegExp(`^(${util.cleanRegex(pattern.source)}|null)$`) : undefined;
		    });
		    util.defineLazyInternal(inst, "values", (zod) => {
		        return zod.def.innerType._zod.values ? new Set([...zod.def.innerType._zod.values, null]) : undefined;
		    });
		    inst._zod.parse = (payload, ctx) => {
		        // Forward direction (decode): allow null to pass through
		        if (payload.value === null)
		            return payload;
		        return def.innerType._zod.run(payload, ctx);
		    };
		});
		exports.$ZodDefault = core.$constructor("$ZodDefault", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    // inst._zod.qin = "true";
		    inst._zod.optin = "defaulted";
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.innerType._zod.values);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            return def.innerType._zod.run(payload, ctx);
		        }
		        // Forward direction (decode): apply defaults for undefined input
		        if (payload.value === undefined) {
		            payload.value = def.defaultValue;
		            /**
		             * $ZodDefault returns the default value immediately in forward direction.
		             * It doesn't pass the default value into the validator ("prefault"). There's no reason to pass the default value through validation. The validity of the default is enforced by TypeScript statically. Otherwise, it's the responsibility of the user to ensure the default is valid. In the case of pipes with divergent in/out types, you can specify the default on the `in` schema of your ZodPipe to set a "prefault" for the pipe.   */
		            return payload;
		        }
		        // Forward direction: continue with default handling
		        const result = def.innerType._zod.run(payload, ctx);
		        if (result instanceof Promise) {
		            return result.then((result) => handleDefaultResult(result, def));
		        }
		        return handleDefaultResult(result, def);
		    };
		});
		function handleDefaultResult(payload, def) {
		    if (payload.value === undefined) {
		        payload.value = def.defaultValue;
		    }
		    return payload;
		}
		exports.$ZodPrefault = core.$constructor("$ZodPrefault", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.optin = "defaulted";
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.innerType._zod.values);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            return def.innerType._zod.run(payload, ctx);
		        }
		        // Forward direction (decode): apply prefault for undefined input
		        if (payload.value === undefined) {
		            payload.value = def.defaultValue;
		        }
		        return def.innerType._zod.run(payload, ctx);
		    };
		});
		exports.$ZodNonOptional = core.$constructor("$ZodNonOptional", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "values", (zod) => {
		        const v = zod.def.innerType._zod.values;
		        return v ? new Set([...v].filter((x) => x !== undefined)) : undefined;
		    });
		    inst._zod.parse = (payload, ctx) => {
		        const result = def.innerType._zod.run(payload, ctx);
		        if (result instanceof Promise) {
		            return result.then((result) => handleNonOptionalResult(result, inst));
		        }
		        return handleNonOptionalResult(result, inst);
		    };
		});
		function handleNonOptionalResult(payload, inst) {
		    if (!payload.issues.length && payload.value === undefined) {
		        payload.issues.push({
		            code: "invalid_type",
		            expected: "nonoptional",
		            input: payload.value,
		            inst,
		        });
		    }
		    return payload;
		}
		exports.$ZodSuccess = core.$constructor("$ZodSuccess", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            throw new core.$ZodEncodeError("ZodSuccess");
		        }
		        const result = def.innerType._zod.run(payload, ctx);
		        if (result instanceof Promise) {
		            return result.then((result) => {
		                payload.value = result.issues.length === 0;
		                return payload;
		            });
		        }
		        payload.value = result.issues.length === 0;
		        return payload;
		    };
		});
		function handleCatchResult(payload, result, def, ctx) {
		    if (!result.issues.length) {
		        payload.value = result.value;
		        // The value carries up, so the flag describing it has to carry with it: a back-edge into a node still being parsed must not be frozen by an enclosing readonly, and its checks belong to the node itself. Guarded so the ordinary case adds no own property.
		        if (result.memo)
		            payload.memo = true;
		        return payload;
		    }
		    // Spread the inner's own payload, not ours: `value` has to stay the input the catch was handed, and the inner ran on a payload of its own so its issues are already private to this call.
		    payload.value = def.catchValue({
		        ...result,
		        value: payload.value,
		        error: {
		            issues: result.issues.map((iss) => util.finalizeIssue(iss, ctx, core.config())),
		        },
		        input: payload.value,
		    });
		    return payload;
		}
		exports.$ZodCatch = core.$constructor("$ZodCatch", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.innerType._zod.optin === "defaulted" ? "defaulted" : "optional");
		    util.defineLazyInternal(inst, "optout", (zod) => zod.def.innerType._zod.optout);
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.innerType._zod.values);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            return def.innerType._zod.run(payload, ctx);
		        }
		        // Forward direction (decode): apply catch logic
		        const result = def.innerType._zod.run({ value: payload.value, issues: [] }, ctx);
		        if (result instanceof Promise) {
		            return result.then((result) => handleCatchResult(payload, result, def, ctx));
		        }
		        return handleCatchResult(payload, result, def, ctx);
		    };
		});
		exports.$ZodNaN = core.$constructor("$ZodNaN", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _ctx) => {
		        if (typeof payload.value !== "number" || !Number.isNaN(payload.value)) {
		            payload.issues.push({
		                input: payload.value,
		                inst,
		                expected: "nan",
		                code: "invalid_type",
		            });
		            return payload;
		        }
		        return payload;
		    };
		});
		exports.$ZodPipe = core.$constructor("$ZodPipe", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.in._zod.values);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.in._zod.optin);
		    util.defineLazyInternal(inst, "optout", (zod) => zod.def.out._zod.optout);
		    util.defineLazyInternal(inst, "propValues", (zod) => zod.def.in._zod.propValues);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            const right = def.out._zod.run(payload, ctx);
		            if (right instanceof Promise) {
		                return right.then((right) => handlePipeResult(right, def.in, ctx));
		            }
		            return handlePipeResult(right, def.in, ctx);
		        }
		        const left = def.in._zod.run(payload, ctx);
		        if (left instanceof Promise) {
		            return left.then((left) => handlePipeResult(left, def.out, ctx));
		        }
		        return handlePipeResult(left, def.out, ctx);
		    };
		});
		function handlePipeResult(left, next, ctx) {
		    // Any issue stops the pipe, so a failing refinement never feeds its transform. An unrecognized key is the exception: it describes the input's extra properties, not the value being piped, and an enclosing intersection may yet reconcile it.
		    if (left.issues.some((iss) => iss.code !== "unrecognized_keys")) {
		        // prevent further checks
		        left.aborted = true;
		        return left;
		    }
		    return next._zod.run({ value: left.value, issues: left.issues }, ctx);
		}
		exports.$ZodCodec = core.$constructor("$ZodCodec", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.in._zod.values);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.in._zod.optin);
		    util.defineLazyInternal(inst, "optout", (zod) => zod.def.out._zod.optout);
		    util.defineLazyInternal(inst, "propValues", (zod) => zod.def.in._zod.propValues);
		    inst._zod.parse = (payload, ctx) => {
		        const direction = ctx.direction || "forward";
		        if (direction === "forward") {
		            const left = def.in._zod.run(payload, ctx);
		            if (left instanceof Promise) {
		                return left.then((left) => handleCodecAResult(left, def, ctx));
		            }
		            return handleCodecAResult(left, def, ctx);
		        }
		        else {
		            const right = def.out._zod.run(payload, ctx);
		            if (right instanceof Promise) {
		                return right.then((right) => handleCodecAResult(right, def, ctx));
		            }
		            return handleCodecAResult(right, def, ctx);
		        }
		    };
		});
		function handleCodecAResult(result, def, ctx) {
		    if (result.issues.length) {
		        // prevent further checks
		        result.aborted = true;
		        return result;
		    }
		    const direction = ctx.direction || "forward";
		    if (direction === "forward") {
		        const transformed = def.transform(result.value, result);
		        if (transformed instanceof Promise) {
		            return transformed.then((value) => handleCodecTxResult(result, value, def.out, ctx));
		        }
		        return handleCodecTxResult(result, transformed, def.out, ctx);
		    }
		    else {
		        const transformed = def.reverseTransform(result.value, result);
		        if (transformed instanceof Promise) {
		            return transformed.then((value) => handleCodecTxResult(result, value, def.in, ctx));
		        }
		        return handleCodecTxResult(result, transformed, def.in, ctx);
		    }
		}
		function handleCodecTxResult(left, value, nextSchema, ctx) {
		    // Check if transform added any issues
		    if (left.issues.length) {
		        left.aborted = true;
		        return left;
		    }
		    return nextSchema._zod.run({ value, issues: left.issues }, ctx);
		}
		exports.$ZodPreprocess = core.$constructor("$ZodPreprocess", (inst, def) => {
		    exports.$ZodPipe.init(inst, def);
		});
		exports.$ZodReadonly = core.$constructor("$ZodReadonly", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    util.defineLazyInternal(inst, "propValues", (zod) => zod.def.innerType._zod.propValues);
		    util.defineLazyInternal(inst, "values", (zod) => zod.def.innerType._zod.values);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.def.innerType?._zod?.optin);
		    util.defineLazyInternal(inst, "optout", (zod) => zod.def.innerType?._zod?.optout);
		    inst._zod.parse = (payload, ctx) => {
		        if (ctx.direction === "backward") {
		            return def.innerType._zod.run(payload, ctx);
		        }
		        const result = def.innerType._zod.run(payload, ctx);
		        if (result instanceof Promise) {
		            return result.then(handleReadonlyResult);
		        }
		        return handleReadonlyResult(result);
		    };
		});
		function handleReadonlyResult(payload) {
		    // A repeat visit hands back a node that is still being built; freezing it here would make the rest of its keys fail to assign.
		    if (!payload.memo)
		        payload.value = Object.freeze(payload.value);
		    return payload;
		}
		// a leaf's pattern source with its own checks folded in: the last pattern-carrying check wins, else length bounds narrow the catch-all, else an integer format narrows the number form. the fold lives here instead of on `_zod.pattern` so a bundle without template literals never pays for it
		function leafPattern(schema) {
		    const def = schema._zod.def;
		    let pattern = def.pattern;
		    let isInt = !!def.format?.includes("int");
		    let minimum;
		    let maximum;
		    for (const ch of def.checks ?? []) {
		        const d = ch._zod.def;
		        if (d.pattern)
		            pattern = d.pattern;
		        isInt || (isInt = !!d.format?.includes("int"));
		        const lo = d.minimum ?? d.length;
		        const hi = d.maximum ?? d.length;
		        if (lo !== undefined && (minimum === undefined || lo > minimum))
		            minimum = lo;
		        if (hi !== undefined && (maximum === undefined || hi < maximum))
		            maximum = hi;
		    }
		    if (pattern)
		        return pattern.source;
		    // an empty range matches nothing at runtime, and `{8,5}` is not a legal quantifier
		    if (minimum !== undefined && maximum !== undefined && minimum > maximum)
		        return "(?!)";
		    if (minimum !== undefined || maximum !== undefined)
		        return regexes.string({ minimum, maximum }).source;
		    const own = schema._zod.pattern;
		    return (isInt && own === regexes.number ? regexes.integer : own)?.source;
		}
		// a part's pattern source. a wrapper's pattern embeds its inner pattern's source verbatim, so the folded form is substituted in place without knowing the wrapper's own composition; a union's options are joined the way the union builds its own pattern
		function partPattern(schema) {
		    const def = schema._zod.def;
		    const own = schema._zod.pattern?.source;
		    // lazy resolves its inner on the internals, not the def
		    const inner = def.innerType ?? schema._zod.innerType;
		    if (inner) {
		        const before = inner._zod.pattern?.source;
		        const after = partPattern(inner);
		        if (own && before && after && after !== before) {
		            return own.replace(util.cleanRegex(before), () => util.cleanRegex(after));
		        }
		        return own;
		    }
		    if (def.options) {
		        const sources = def.options.map(partPattern);
		        if (sources.every(Boolean))
		            return `^(${sources.map((s) => util.cleanRegex(s)).join("|")})$`;
		    }
		    return leafPattern(schema);
		}
		exports.$ZodTemplateLiteral = core.$constructor("$ZodTemplateLiteral", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    const regexParts = [];
		    for (const part of def.parts) {
		        if (typeof part === "object" && part !== null) {
		            // is Zod schema
		            const source = partPattern(part);
		            if (!source) {
		                throw new Error(`Invalid template literal part, no pattern found: ${[...part._zod.traits].shift()}`);
		            }
		            regexParts.push(util.cleanRegex(source));
		        }
		        else if (part === null || util.primitiveTypes.has(typeof part)) {
		            regexParts.push(util.escapeRegex(`${part}`));
		        }
		        else {
		            throw new Error(`Invalid template literal part: ${part}`);
		        }
		    }
		    inst._zod.pattern = new RegExp(`^${regexParts.join("")}$`);
		    inst._zod.parse = (payload, _ctx) => {
		        if (typeof payload.value !== "string") {
		            payload.issues.push({
		                input: payload.value,
		                inst,
		                expected: "string",
		                code: "invalid_type",
		            });
		            return payload;
		        }
		        inst._zod.pattern.lastIndex = 0;
		        if (!inst._zod.pattern.test(payload.value)) {
		            payload.issues.push({
		                input: payload.value,
		                inst,
		                code: "invalid_format",
		                format: def.format ?? "template_literal",
		                pattern: inst._zod.pattern.source,
		            });
		            return payload;
		        }
		        return payload;
		    };
		});
		exports.$ZodFunction = core.$constructor("$ZodFunction", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    // Defined, not assigned: the classic prototype exposes `_def` as a getter with no setter.
		    Object.defineProperty(inst, "_def", { value: def });
		    inst._zod.def = def;
		    inst.implement = (func) => {
		        if (typeof func !== "function") {
		            throw new Error("implement() must be called with a function");
		        }
		        // Defined inline so the closure stays anonymous: binding it to a `const` first names it, which costs 256 bytes per implemented function.
		        return Object.defineProperty(function (...args) {
		            const parsedArgs = inst._def.input ? (0, parse_js_1.parse)(inst._def.input, args) : args;
		            const result = Reflect.apply(func, this, parsedArgs);
		            if (inst._def.output) {
		                return (0, parse_js_1.parse)(inst._def.output, result);
		            }
		            return result;
		        }, "_zod", { value: inst._zod, enumerable: false });
		    };
		    inst.implementAsync = (func) => {
		        if (typeof func !== "function") {
		            throw new Error("implementAsync() must be called with a function");
		        }
		        return Object.defineProperty(async function (...args) {
		            const parsedArgs = inst._def.input ? await (0, parse_js_1.parseAsync)(inst._def.input, args) : args;
		            const result = await Reflect.apply(func, this, parsedArgs);
		            if (inst._def.output) {
		                return await (0, parse_js_1.parseAsync)(inst._def.output, result);
		            }
		            return result;
		        }, "_zod", { value: inst._zod, enumerable: false });
		    };
		    inst._zod.parse = (payload, _ctx) => {
		        if (typeof payload.value !== "function") {
		            payload.issues.push({
		                code: "invalid_type",
		                expected: "function",
		                input: payload.value,
		                inst,
		            });
		            return payload;
		        }
		        // Check if output is a promise type to determine if we should use async implementation
		        const hasPromiseOutput = inst._def.output && inst._def.output._zod.def.type === "promise";
		        if (hasPromiseOutput) {
		            payload.value = inst.implementAsync(payload.value);
		        }
		        else {
		            payload.value = inst.implement(payload.value);
		        }
		        return payload;
		    };
		    inst.input = (...args) => {
		        const F = inst.constructor;
		        if (Array.isArray(args[0])) {
		            return new F({
		                type: "function",
		                input: new exports.$ZodTuple({
		                    type: "tuple",
		                    items: args[0],
		                    rest: args[1],
		                }),
		                output: inst._def.output,
		            });
		        }
		        return new F({
		            type: "function",
		            input: args[0],
		            output: inst._def.output,
		        });
		    };
		    inst.output = (output) => {
		        const F = inst.constructor;
		        return new F({
		            type: "function",
		            input: inst._def.input,
		            output,
		        });
		    };
		    return inst;
		});
		exports.$ZodPromise = core.$constructor("$ZodPromise", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, ctx) => {
		        return Promise.resolve(payload.value).then((inner) => def.innerType._zod.run({ value: inner, issues: [] }, ctx));
		    };
		});
		exports.$ZodLazy = core.$constructor("$ZodLazy", (inst, def) => {
		    exports.$ZodType.init(inst, def);
		    // Cache the resolved inner type on the shared `def` so all clones of this lazy (e.g. via `.describe()`/`.meta()`) share the same inner instance, preserving identity for cycle detection on recursive schemas.
		    util.defineLazy(inst._zod, "innerType", () => {
		        const d = def;
		        if (!d._cachedInner)
		            d._cachedInner = def.getter();
		        return d._cachedInner;
		    });
		    util.defineLazyInternal(inst, "pattern", (zod) => zod.innerType?._zod?.pattern);
		    util.defineLazyInternal(inst, "propValues", (zod) => zod.innerType?._zod?.propValues);
		    util.defineLazyInternal(inst, "optin", (zod) => zod.innerType?._zod?.optin ?? undefined);
		    util.defineLazyInternal(inst, "optout", (zod) => zod.innerType?._zod?.optout ?? undefined);
		    inst._zod.parse = (payload, ctx) => {
		        const inner = inst._zod.innerType;
		        return inner._zod.run(payload, ctx);
		    };
		});
		exports.$ZodCustom = core.$constructor("$ZodCustom", (inst, def) => {
		    checks.$ZodCheck.init(inst, def);
		    exports.$ZodType.init(inst, def);
		    inst._zod.parse = (payload, _) => {
		        return payload;
		    };
		    inst._zod.check = (payload) => {
		        const input = payload.value;
		        const r = def.fn(input);
		        if (r instanceof Promise) {
		            return r.then((r) => handleRefineResult(r, payload, input, inst));
		        }
		        handleRefineResult(r, payload, input, inst);
		        return;
		    };
		});
		function handleRefineResult(result, payload, input, inst) {
		    if (!result) {
		        const _iss = {
		            code: "custom",
		            input,
		            inst, // incorporates params.error into issue reporting
		            path: [...(inst._zod.def.path ?? [])], // incorporates params.error into issue reporting
		            continue: !inst._zod.def.abort,
		            // params: inst._zod.def.params,
		        };
		        if (inst._zod.def.params)
		            _iss.params = inst._zod.def.params;
		        payload.issues.push(util.issue(_iss));
		    }
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (schemas$1));
	return schemas$1;
}

var memoizer = {};

var hasRequiredMemoizer;

function requireMemoizer () {
	if (hasRequiredMemoizer) return memoizer;
	hasRequiredMemoizer = 1;
	(function (exports) {
		var __createBinding = (memoizer && memoizer.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (memoizer && memoizer.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (memoizer && memoizer.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.$ZodCyclicError = void 0;
		exports.isRecursiveSchema = isRecursiveSchema;
		exports.memoizer = memoizer$1;
		exports.isBackEdge = isBackEdge;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		class $ZodCyclicError extends Error {
		    constructor() {
		        super(`Cannot parse a reference cycle that closes through a transform`);
		        this.name = "ZodCyclicError";
		    }
		}
		exports.$ZodCyclicError = $ZodCyclicError;
		/** Keyed off the context object every schema in one parse call already shares. */
		const STATE = "~memo";
		const NO_ISSUES = [];
		// a value a cycle can close through
		function isRef(value) {
		    return value !== null && typeof value === "object";
		}
		// Receivers prefix paths in place, so the cache and every hand-out need their own copies.
		function cloneIssues(issues) {
		    return issues.map((iss) => (iss.path ? { ...iss, path: iss.path.slice() } : { ...iss }));
		}
		const recursive = /*@__PURE__*/ new WeakMap();
		/** What the walk established, in order of certainty: ordered so the strongest answer among children wins. */
		const NONE = 0;
		const ASSUMED = 1;
		const PROVEN = 2;
		/** Whether this schema's subtree contains a cycle, so one parse can re-enter it. */
		function isRecursive(inst, stack, resolve) {
		    const cached = recursive.get(inst);
		    if (cached !== undefined)
		        return cached ? PROVEN : NONE;
		    // Relative to the walk in progress, so not cached.
		    if (stack.has(inst))
		        return PROVEN;
		    stack.add(inst);
		    let result = NONE;
		    const check = (child) => {
		        if (result !== PROVEN && child?._zod) {
		            const answer = isRecursive(child, stack, resolve);
		            if (answer > result)
		                result = answer;
		        }
		    };
		    // `Reflect.ownKeys` rather than `Object.keys`, so a cycle through a declared symbol key is still seen
		    const shape = (sh, spread) => {
		        let answer = NONE;
		        for (const key of Reflect.ownKeys(sh)) {
		            const desc = Object.getOwnPropertyDescriptor(sh, key);
		            // an object resolves its shape by spread, so a key it does not enumerate is never parsed; `z.properties` reads every own key and so keeps them all
		            if (spread && !desc.enumerable)
		                continue;
		            // resolving runs user code, and a factory mints a fresh subtree per read, so an edge the walk can't follow counts as a cycle
		            const child = desc.get ? ASSUMED : desc.value?._zod ? isRecursive(desc.value, stack, resolve) : NONE;
		            if (child > answer)
		                answer = child;
		        }
		        return answer;
		    };
		    const merge = (answer) => {
		        if (answer > result)
		            result = answer;
		    };
		    const def = inst._zod.def;
		    const kind = def.type;
		    switch (kind) {
		        case "object": {
		            const raw = util.rawShape(def);
		            // a def with no raw shape answers `shape` from an accessor of its own, and running that can mint a whole fresh subtree
		            merge(raw ? shape(raw, true) : ASSUMED);
		            check(def.catchall);
		            break;
		        }
		        case "array":
		            check(def.element);
		            break;
		        case "tuple":
		            for (const el of def.items)
		                check(el);
		            check(def.rest);
		            break;
		        case "record":
		        case "map":
		            check(def.keyType);
		            check(def.valueType);
		            break;
		        case "set":
		            check(def.valueType);
		            break;
		        case "union":
		            for (const el of def.options)
		                check(el);
		            break;
		        case "intersection":
		            check(def.left);
		            check(def.right);
		            break;
		        case "optional":
		        case "nullable":
		        case "default":
		        case "prefault":
		        case "catch":
		        case "readonly":
		        case "nonoptional":
		        case "promise":
		        case "success":
		            check(def.innerType);
		            break;
		        case "pipe":
		            check(def.in);
		            check(def.out);
		            break;
		        case "function":
		            check(def.input);
		            check(def.output);
		            break;
		        // `$ZodLazy` caches its inner on the def, so a resolved edge is followed exactly
		        case "lazy": {
		            const inner = def._cachedInner ?? (resolve ? inst._zod.innerType : undefined);
		            // walked with resolution off: one hop sees past the deferral, and a lazy that yields only another unresolved lazy is generative, so it stops there
		            merge(inner ? isRecursive(inner, stack, false) : ASSUMED);
		            break;
		        }
		        // a leaf by choice: `parts` are regex fragments, not data positions
		        case "template_literal":
		        // leaves
		        case "string":
		        case "number":
		        case "int":
		        case "boolean":
		        case "bigint":
		        case "symbol":
		        case "undefined":
		        case "null":
		        case "void":
		        case "never":
		        case "any":
		        case "unknown":
		        case "date":
		        case "nan":
		        case "enum":
		        case "literal":
		        case "file":
		        case "transform":
		        case "custom":
		            break;
		        default: {
		            // a user-defined kind can still hold children, and only its author knows where, so fall back to scanning the def — skipping accessors, since reading one can run user code
		            for (const key in def) {
		                const desc = Object.getOwnPropertyDescriptor(def, key);
		                if (!desc || desc.get)
		                    continue;
		                const value = desc.value;
		                if (!value || typeof value !== "object")
		                    continue;
		                if (value._zod)
		                    check(value);
		                else if (Array.isArray(value))
		                    for (const el of value)
		                        check(el);
		            }
		        }
		    }
		    stack.delete(inst);
		    return settle(inst, result);
		}
		/** An assumed answer must not outlive the resolution that settles it, so only a certain one is cached. */
		function settle(inst, answer) {
		    if (answer !== ASSUMED)
		        recursive.set(inst, answer === PROVEN);
		    return answer;
		}
		/**
		 * Whether one parse can re-enter this schema, i.e. its subtree contains a cycle.
		 * Exported for `z.compile`, which refuses to compile such a schema: cycle
		 * breaking is driven from here off state keyed on the parse context, and a
		 * generated fast path has no context to key on.
		 */
		function isRecursiveSchema(inst) {
		    // z.compile never parses, so nothing would ever resolve a lazy for it; it runs once and already treats a throw here as recursive
		    return isRecursive(inst, new Set(), true) !== NONE;
		}
		function bucketFor(state, inst) {
		    let bucket = state.buckets.get(inst);
		    if (!bucket) {
		        bucket = new WeakMap();
		        state.buckets.set(inst, bucket);
		    }
		    return bucket;
		}
		// Set immediately before delegating to core and cleared immediately after, so `alloc` registers only for a visit this module is driving.
		let handoff;
		// Allocated but unfinished entries. `alloc` and the matching pop both happen in the synchronous part of a parse, so they nest even when children are async, and one stack serves every schema.
		const open = [];
		const memo = {
		    alloc(_inst, payload, empty) {
		        const bucket = handoff;
		        if (!bucket)
		            return empty;
		        handoff = undefined;
		        const entry = { value: empty, issues: null };
		        bucket.set(payload.value, entry);
		        open.push(entry);
		        return empty;
		    },
		    guard(inst) {
		        var _a;
		        (_a = inst._zod).deferred ?? (_a.deferred = []);
		        inst._zod.deferred.push(() => {
		            const base = inst._zod.parse;
		            const wrapped = (payload, ctx) => {
		                // The value is a placeholder a back-edge is still waiting on, so the cycle closes through this transform. Its output can't exist in time to bind.
		                if (ctx.direction !== "backward" && isBackEdge(ctx, payload.value))
		                    throw new $ZodCyclicError();
		                return base(payload, ctx);
		            };
		            inst._zod.parse = wrapped;
		            if (inst._zod.run === base)
		                inst._zod.run = wrapped;
		        });
		    },
		    attach(inst) {
		        var _a;
		        let isRecursiveInst;
		        let rechecked = false;
		        // a recursive schema is re-entered many times per parse and its bucket never changes
		        let lastCtx;
		        let lastBucket;
		        // Wraps `parse` in a deferred so it sees the container's final parse. Core's own deferred copies `parse` into `run` when there are no checks, and it ran first, so `run` is patched to match; with checks, `run` reads `parse` dynamically.
		        (_a = inst._zod).deferred ?? (_a.deferred = []);
		        inst._zod.deferred.push(() => {
		            const base = inst._zod.parse;
		            const wrapped = (payload, ctx) => {
		                if (isRecursiveInst === undefined) {
		                    const walked = isRecursive(inst, new Set(), false);
		                    if (walked === NONE) {
		                        // Nothing here can ever fire, so take it back out.
		                        inst._zod.parse = base;
		                        if (inst._zod.run === wrapped)
		                            inst._zod.run = base;
		                        return base(payload, ctx);
		                    }
		                    // this parse resolves the deferred edges on its own path, so ask once more before latching
		                    if (walked === PROVEN || rechecked)
		                        isRecursiveInst = true;
		                    else
		                        rechecked = true;
		                }
		                const input = payload.value;
		                if (!isRef(input))
		                    return base(payload, ctx);
		                let state = ctx[STATE];
		                if (!state) {
		                    state = { buckets: new WeakMap(), backEdges: undefined };
		                    ctx[STATE] = state;
		                }
		                let bucket;
		                if (lastCtx === ctx) {
		                    bucket = lastBucket;
		                }
		                else {
		                    bucket = bucketFor(state, inst);
		                    lastCtx = ctx;
		                    lastBucket = bucket;
		                }
		                const hit = bucket.get(input);
		                if (hit) {
		                    payload.value = hit.value;
		                    if (hit.issues) {
		                        if (hit.issues.length)
		                            payload.issues.push(...cloneIssues(hit.issues));
		                    }
		                    else {
		                        // Still being parsed: its own checks cover it, so skip them here.
		                        payload.memo = true;
		                        state.backEdges ?? (state.backEdges = new WeakSet());
		                        state.backEdges.add(hit.value);
		                    }
		                    return payload;
		                }
		                handoff = bucket;
		                const depth = open.length;
		                const result = base(payload, ctx);
		                handoff = undefined;
		                // A container that rejected its input outright allocated nothing.
		                const entry = open.length > depth ? open.pop() : undefined;
		                // Both paths written out so the sync one allocates no closure. It runs once per node, and capturing here cost more than everything else combined.
		                if (result instanceof Promise) {
		                    return result.then((r) => {
		                        if (entry)
		                            entry.issues = r.issues.length ? cloneIssues(r.issues) : NO_ISSUES;
		                        return r;
		                    });
		                }
		                if (entry)
		                    entry.issues = result.issues.length ? cloneIssues(result.issues) : NO_ISSUES;
		                return result;
		            };
		            inst._zod.parse = wrapped;
		            if (inst._zod.run === base)
		                inst._zod.run = wrapped;
		        });
		    },
		};
		/** The memoizer that gives containers cycle support. `zod` installs it by default; `zod/mini` opts in with `config({ memoizer: memoizer() })`. */
		function memoizer$1() {
		    return memo;
		}
		/** Whether this value is a node a back-edge resolved to before it finished. */
		function isBackEdge(ctx, value) {
		    const backEdges = ctx[STATE]?.backEdges;
		    return backEdges !== undefined && isRef(value) && backEdges.has(value);
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (memoizer));
	return memoizer;
}

var locales = {};

var ar$1 = {exports: {}};

var ar = ar$1.exports;

var hasRequiredAr;

function requireAr () {
	if (hasRequiredAr) return ar$1.exports;
	hasRequiredAr = 1;
	(function (module, exports) {
		var __createBinding = (ar && ar.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ar && ar.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ar && ar.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "حرف", verb: "أن يحوي" },
		        file: { unit: "بايت", verb: "أن يحوي" },
		        array: { unit: "عنصر", verb: "أن يحوي" },
		        set: { unit: "عنصر", verb: "أن يحوي" },
		        map: { unit: "عنصر", verb: "أن يحوي" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "مدخل",
		        email: "بريد إلكتروني",
		        url: "رابط",
		        emoji: "إيموجي",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "تاريخ ووقت بمعيار ISO",
		        date: "تاريخ بمعيار ISO",
		        time: "وقت بمعيار ISO",
		        duration: "مدة بمعيار ISO",
		        ipv4: "عنوان IPv4",
		        ipv6: "عنوان IPv6",
		        mac: "عنوان MAC",
		        cidrv4: "مدى عناوين بصيغة IPv4",
		        cidrv6: "مدى عناوين بصيغة IPv6",
		        base64: "نَص بترميز base64-encoded",
		        base64url: "نَص بترميز base64url-encoded",
		        json_string: "نَص على هيئة JSON",
		        e164: "رقم هاتف بمعيار E.164",
		        credit_card: "رقم بطاقة الائتمان",
		        currency_code: "رمز العملة",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "مدخل",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `مدخلات غير مقبولة: يفترض إدخال instanceof ${issue.expected}، ولكن تم إدخال ${received}`;
		                }
		                return `مدخلات غير مقبولة: يفترض إدخال ${expected}، ولكن تم إدخال ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `مدخلات غير مقبولة: يفترض إدخال ${util.stringifyPrimitive(issue.values[0])}`;
		                return `اختيار غير مقبول: يتوقع انتقاء أحد هذه الخيارات: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return ` أكبر من اللازم: يفترض أن تكون ${issue.origin ?? "القيمة"} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "عنصر"}`;
		                return `أكبر من اللازم: يفترض أن تكون ${issue.origin ?? "القيمة"} ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `أصغر من اللازم: يفترض لـ ${issue.origin} أن يكون ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `أصغر من اللازم: يفترض لـ ${issue.origin} أن يكون ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `نَص غير مقبول: يجب أن يبدأ بـ "${issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `نَص غير مقبول: يجب أن ينتهي بـ "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `نَص غير مقبول: يجب أن يتضمَّن "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `نَص غير مقبول: يجب أن يطابق النمط ${_issue.pattern}`;
		                return `${FormatDictionary[_issue.format] ?? issue.format} غير مقبول`;
		            }
		            case "not_multiple_of":
		                return `رقم غير مقبول: يجب أن يكون من مضاعفات ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `معرف${issue.keys.length > 1 ? "ات" : ""} غريب${issue.keys.length > 1 ? "ة" : ""}: ${util.joinValues(issue.keys, "، ")}`;
		            case "invalid_key":
		                return `معرف غير مقبول في ${issue.origin}`;
		            case "invalid_union":
		                return "مدخل غير مقبول";
		            case "invalid_element":
		                return `مدخل غير مقبول في ${issue.origin}`;
		            default:
		                return "مدخل غير مقبول";
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ar$1, ar$1.exports));
	return ar$1.exports;
}

var az$1 = {exports: {}};

var az = az$1.exports;

var hasRequiredAz;

function requireAz () {
	if (hasRequiredAz) return az$1.exports;
	hasRequiredAz = 1;
	(function (module, exports) {
		var __createBinding = (az && az.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (az && az.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (az && az.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "simvol", verb: "olmalıdır" },
		        file: { unit: "bayt", verb: "olmalıdır" },
		        array: { unit: "element", verb: "olmalıdır" },
		        set: { unit: "element", verb: "olmalıdır" },
		        map: { unit: "element", verb: "olmalıdır" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "email address",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO datetime",
		        date: "ISO date",
		        time: "ISO time",
		        duration: "ISO duration",
		        ipv4: "IPv4 address",
		        ipv6: "IPv6 address",
		        mac: "MAC address",
		        cidrv4: "IPv4 range",
		        cidrv6: "IPv6 range",
		        base64: "base64-encoded string",
		        base64url: "base64url-encoded string",
		        json_string: "JSON string",
		        e164: "E.164 number",
		        credit_card: "kredit kartı nömrəsi",
		        currency_code: "valyuta kodu",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Yanlış dəyər: gözlənilən instanceof ${issue.expected}, daxil olan ${received}`;
		                }
		                return `Yanlış dəyər: gözlənilən ${expected}, daxil olan ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Yanlış dəyər: gözlənilən ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Yanlış seçim: aşağıdakılardan biri olmalıdır: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Çox böyük: gözlənilən ${issue.origin ?? "dəyər"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "element"}`;
		                return `Çox böyük: gözlənilən ${issue.origin ?? "dəyər"} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Çox kiçik: gözlənilən ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                return `Çox kiçik: gözlənilən ${issue.origin} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Yanlış mətn: "${_issue.prefix}" ilə başlamalıdır`;
		                if (_issue.format === "ends_with")
		                    return `Yanlış mətn: "${_issue.suffix}" ilə bitməlidir`;
		                if (_issue.format === "includes")
		                    return `Yanlış mətn: "${_issue.includes}" daxil olmalıdır`;
		                if (_issue.format === "regex")
		                    return `Yanlış mətn: ${_issue.pattern} şablonuna uyğun olmalıdır`;
		                return `Yanlış ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Yanlış ədəd: ${issue.divisor} ilə bölünə bilən olmalıdır`;
		            case "unrecognized_keys":
		                return `Tanınmayan açar${issue.keys.length > 1 ? "lar" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} daxilində yanlış açar`;
		            case "invalid_union":
		                return "Yanlış dəyər";
		            case "invalid_element":
		                return `${issue.origin} daxilində yanlış dəyər`;
		            default:
		                return `Yanlış dəyər`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (az$1, az$1.exports));
	return az$1.exports;
}

var be$1 = {exports: {}};

var be = be$1.exports;

var hasRequiredBe;

function requireBe () {
	if (hasRequiredBe) return be$1.exports;
	hasRequiredBe = 1;
	(function (module, exports) {
		var __createBinding = (be && be.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (be && be.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (be && be.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		function getBelarusianPlural(count, one, few, many) {
		    const absCount = Math.abs(count);
		    const lastDigit = absCount % 10;
		    const lastTwoDigits = absCount % 100;
		    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
		        return many;
		    }
		    if (lastDigit === 1) {
		        return one;
		    }
		    if (lastDigit >= 2 && lastDigit <= 4) {
		        return few;
		    }
		    return many;
		}
		const error = () => {
		    const Sizable = {
		        string: {
		            unit: {
		                one: "сімвал",
		                few: "сімвалы",
		                many: "сімвалаў",
		            },
		            verb: "мець",
		        },
		        array: {
		            unit: {
		                one: "элемент",
		                few: "элементы",
		                many: "элементаў",
		            },
		            verb: "мець",
		        },
		        set: {
		            unit: {
		                one: "элемент",
		                few: "элементы",
		                many: "элементаў",
		            },
		            verb: "мець",
		        },
		        map: {
		            unit: {
		                one: "элемент",
		                few: "элементы",
		                many: "элементаў",
		            },
		            verb: "мець",
		        },
		        file: {
		            unit: {
		                one: "байт",
		                few: "байты",
		                many: "байтаў",
		            },
		            verb: "мець",
		        },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "увод",
		        email: "email адрас",
		        url: "URL",
		        emoji: "эмодзі",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO дата і час",
		        date: "ISO дата",
		        time: "ISO час",
		        duration: "ISO працягласць",
		        ipv4: "IPv4 адрас",
		        ipv6: "IPv6 адрас",
		        mac: "MAC адрас",
		        cidrv4: "IPv4 дыяпазон",
		        cidrv6: "IPv6 дыяпазон",
		        base64: "радок у фармаце base64",
		        base64url: "радок у фармаце base64url",
		        json_string: "JSON радок",
		        e164: "нумар E.164",
		        credit_card: "нумар крэдытнай карты",
		        currency_code: "код валюты",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "увод",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "лік",
		        array: "масіў",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Няправільны ўвод: чакаўся instanceof ${issue.expected}, атрымана ${received}`;
		                }
		                return `Няправільны ўвод: чакаўся ${expected}, атрымана ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Няправільны ўвод: чакалася ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Няправільны варыянт: чакаўся адзін з ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    const maxValue = Number(issue.maximum);
		                    const unit = getBelarusianPlural(maxValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
		                    return `Занадта вялікі: чакалася, што ${issue.origin ?? "значэнне"} павінна ${sizing.verb} ${adj}${issue.maximum.toString()} ${unit}`;
		                }
		                return `Занадта вялікі: чакалася, што ${issue.origin ?? "значэнне"} павінна быць ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    const minValue = Number(issue.minimum);
		                    const unit = getBelarusianPlural(minValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
		                    return `Занадта малы: чакалася, што ${issue.origin} павінна ${sizing.verb} ${adj}${issue.minimum.toString()} ${unit}`;
		                }
		                return `Занадта малы: чакалася, што ${issue.origin} павінна быць ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Няправільны радок: павінен пачынацца з "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Няправільны радок: павінен заканчвацца на "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Няправільны радок: павінен змяшчаць "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Няправільны радок: павінен адпавядаць шаблону ${_issue.pattern}`;
		                return `Няправільны ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Няправільны лік: павінен быць кратным ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Нераспазнаны ${issue.keys.length > 1 ? "ключы" : "ключ"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Няправільны ключ у ${issue.origin}`;
		            case "invalid_union":
		                return "Няправільны ўвод";
		            case "invalid_element":
		                return `Няправільнае значэнне ў ${issue.origin}`;
		            default:
		                return `Няправільны ўвод`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (be$1, be$1.exports));
	return be$1.exports;
}

var bg$1 = {exports: {}};

var bg = bg$1.exports;

var hasRequiredBg;

function requireBg () {
	if (hasRequiredBg) return bg$1.exports;
	hasRequiredBg = 1;
	(function (module, exports) {
		var __createBinding = (bg && bg.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (bg && bg.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (bg && bg.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "символа", verb: "да съдържа" },
		        file: { unit: "байта", verb: "да съдържа" },
		        array: { unit: "елемента", verb: "да съдържа" },
		        set: { unit: "елемента", verb: "да съдържа" },
		        map: { unit: "елемента", verb: "да съдържа" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "вход",
		        email: "имейл адрес",
		        url: "URL",
		        emoji: "емоджи",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO време",
		        date: "ISO дата",
		        time: "ISO време",
		        duration: "ISO продължителност",
		        ipv4: "IPv4 адрес",
		        ipv6: "IPv6 адрес",
		        mac: "MAC адрес",
		        cidrv4: "IPv4 диапазон",
		        cidrv6: "IPv6 диапазон",
		        base64: "base64-кодиран низ",
		        base64url: "base64url-кодиран низ",
		        json_string: "JSON низ",
		        e164: "E.164 номер",
		        credit_card: "номер на кредитна карта",
		        currency_code: "код на валута",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "вход",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "число",
		        array: "масив",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Невалиден вход: очакван instanceof ${issue.expected}, получен ${received}`;
		                }
		                return `Невалиден вход: очакван ${expected}, получен ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Невалиден вход: очакван ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Невалидна опция: очаквано едно от ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Твърде голямо: очаква се ${issue.origin ?? "стойност"} да съдържа ${adj}${issue.maximum.toString()} ${sizing.unit ?? "елемента"}`;
		                return `Твърде голямо: очаква се ${issue.origin ?? "стойност"} да бъде ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Твърде малко: очаква се ${issue.origin} да съдържа ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Твърде малко: очаква се ${issue.origin} да бъде ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Невалиден низ: трябва да започва с "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Невалиден низ: трябва да завършва с "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Невалиден низ: трябва да включва "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Невалиден низ: трябва да съвпада с ${_issue.pattern}`;
		                let invalid_adj = "Невалиден";
		                if (_issue.format === "emoji")
		                    invalid_adj = "Невалидно";
		                if (_issue.format === "datetime")
		                    invalid_adj = "Невалидно";
		                if (_issue.format === "date")
		                    invalid_adj = "Невалидна";
		                if (_issue.format === "time")
		                    invalid_adj = "Невалидно";
		                if (_issue.format === "duration")
		                    invalid_adj = "Невалидна";
		                return `${invalid_adj} ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Невалидно число: трябва да бъде кратно на ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Неразпознат${issue.keys.length > 1 ? "и" : ""} ключ${issue.keys.length > 1 ? "ове" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Невалиден ключ в ${issue.origin}`;
		            case "invalid_union":
		                return "Невалиден вход";
		            case "invalid_element":
		                return `Невалидна стойност в ${issue.origin}`;
		            default:
		                return `Невалиден вход`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (bg$1, bg$1.exports));
	return bg$1.exports;
}

var bn$1 = {exports: {}};

var bn = bn$1.exports;

var hasRequiredBn;

function requireBn () {
	if (hasRequiredBn) return bn$1.exports;
	hasRequiredBn = 1;
	(function (module, exports) {
		var __createBinding = (bn && bn.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (bn && bn.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (bn && bn.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "অক্ষর", verb: "থাকতে হবে" },
		        file: { unit: "বাইট", verb: "থাকতে হবে" },
		        array: { unit: "আইটেম", verb: "থাকতে হবে" },
		        set: { unit: "আইটেম", verb: "থাকতে হবে" },
		        map: { unit: "এন্ট্রি", verb: "থাকতে হবে" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ইনপুট",
		        email: "ইমেইল ঠিকানা",
		        url: "URL",
		        emoji: "ইমোজি",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO তারিখ ও সময়",
		        date: "ISO তারিখ",
		        time: "ISO সময়",
		        duration: "ISO সময়কাল",
		        ipv4: "IPv4 ঠিকানা",
		        ipv6: "IPv6 ঠিকানা",
		        mac: "MAC ঠিকানা",
		        cidrv4: "IPv4 রেঞ্জ",
		        cidrv6: "IPv6 রেঞ্জ",
		        base64: "base64-এনকোডেড স্ট্রিং",
		        base64url: "base64url-এনকোডেড স্ট্রিং",
		        json_string: "JSON স্ট্রিং",
		        e164: "E.164 নম্বর",
		        credit_card: "ক্রেডিট কার্ড নম্বর",
		        currency_code: "মুদ্রা কোড",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ইনপুট",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `অবৈধ ইনপুট: প্রত্যাশিত ${expected}, প্রাপ্ত ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `অবৈধ ইনপুট: প্রত্যাশিত ${util.stringifyPrimitive(issue.values[0])}`;
		                return `অবৈধ অপশন: ${util.joinValues(issue.values, " | ")} এর মধ্যে একটি প্রত্যাশিত`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `অনেক বড়: ${issue.origin ?? "মান"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "এলিমেন্ট"} হতে হবে`;
		                return `অনেক বড়: ${issue.origin ?? "মান"} ${adj}${issue.maximum.toString()} হতে হবে`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `অনেক ছোট: ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} হতে হবে`;
		                }
		                return `অনেক ছোট: ${issue.origin} ${adj}${issue.minimum.toString()} হতে হবে`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `অবৈধ স্ট্রিং: "${_issue.prefix}" দিয়ে শুরু হতে হবে`;
		                }
		                if (_issue.format === "ends_with")
		                    return `অবৈধ স্ট্রিং: "${_issue.suffix}" দিয়ে শেষ হতে হবে`;
		                if (_issue.format === "includes")
		                    return `অবৈধ স্ট্রিং: "${_issue.includes}" অন্তর্ভুক্ত থাকতে হবে`;
		                if (_issue.format === "regex")
		                    return `অবৈধ স্ট্রিং: ${_issue.pattern} প্যাটার্ন মিলতে হবে`;
		                return `অবৈধ ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `অবৈধ নম্বর: ${issue.divisor} এর গুণিতক হতে হবে`;
		            case "unrecognized_keys":
		                return `অচেনা কী${issue.keys.length > 1 ? "গুলো" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} এ অবৈধ কী`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `অবৈধ ডিসক্রিমিনেটর মান। প্রত্যাশিত ${opts}`;
		                }
		                return "অবৈধ ইনপুট";
		            case "invalid_element":
		                return `${issue.origin} এ অবৈধ মান`;
		            default:
		                return "অবৈধ ইনপুট";
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (bn$1, bn$1.exports));
	return bn$1.exports;
}

var ca$1 = {exports: {}};

var ca = ca$1.exports;

var hasRequiredCa;

function requireCa () {
	if (hasRequiredCa) return ca$1.exports;
	hasRequiredCa = 1;
	(function (module, exports) {
		var __createBinding = (ca && ca.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ca && ca.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ca && ca.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caràcters", verb: "contenir" },
		        file: { unit: "bytes", verb: "contenir" },
		        array: { unit: "elements", verb: "contenir" },
		        set: { unit: "elements", verb: "contenir" },
		        map: { unit: "elements", verb: "contenir" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "entrada",
		        email: "adreça electrònica",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "data i hora ISO",
		        date: "data ISO",
		        time: "hora ISO",
		        duration: "durada ISO",
		        ipv4: "adreça IPv4",
		        ipv6: "adreça IPv6",
		        mac: "adreça MAC",
		        cidrv4: "rang IPv4",
		        cidrv6: "rang IPv6",
		        base64: "cadena codificada en base64",
		        base64url: "cadena codificada en base64url",
		        json_string: "cadena JSON",
		        e164: "número E.164",
		        credit_card: "número de targeta de crèdit",
		        currency_code: "codi de moneda",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "entrada",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Tipus invàlid: s'esperava instanceof ${issue.expected}, s'ha rebut ${received}`;
		                }
		                return `Tipus invàlid: s'esperava ${expected}, s'ha rebut ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Valor invàlid: s'esperava ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Opció invàlida: s'esperava una de ${util.joinValues(issue.values, " o ")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "com a màxim" : "menys de";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Massa gran: s'esperava que ${issue.origin ?? "el valor"} contingués ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "elements"}`;
		                return `Massa gran: s'esperava que ${issue.origin ?? "el valor"} fos ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? "com a mínim" : "més de";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Massa petit: s'esperava que ${issue.origin} contingués ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Massa petit: s'esperava que ${issue.origin} fos ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Format invàlid: ha de començar amb "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Format invàlid: ha d'acabar amb "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Format invàlid: ha d'incloure "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Format invàlid: ha de coincidir amb el patró ${_issue.pattern}`;
		                return `Format invàlid per a ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Número invàlid: ha de ser múltiple de ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Clau${issue.keys.length > 1 ? "s" : ""} no reconeguda${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Clau invàlida a ${issue.origin}`;
		            case "invalid_union":
		                return "Entrada invàlida"; // Could also be "Tipus d'unió invàlid" but "Entrada invàlida" is more general
		            case "invalid_element":
		                return `Element invàlid a ${issue.origin}`;
		            default:
		                return `Entrada invàlida`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ca$1, ca$1.exports));
	return ca$1.exports;
}

var ckb$1 = {exports: {}};

var ckb = ckb$1.exports;

var hasRequiredCkb;

function requireCkb () {
	if (hasRequiredCkb) return ckb$1.exports;
	hasRequiredCkb = 1;
	(function (module, exports) {
		var __createBinding = (ckb && ckb.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ckb && ckb.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ckb && ckb.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "پیت", verb: "بێت" },
		        file: { unit: "بایت", verb: "بێت" },
		        array: { unit: "دانە", verb: "بێت" },
		        set: { unit: "دانە", verb: "بێت" },
		        map: { unit: "دانە", verb: "بێت" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "regex",
		        email: "ئیمەیڵ",
		        url: "بەستەر (URL)",
		        emoji: "ئیمۆجی",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ڕێکەوت و کات",
		        date: "ڕێکەوت",
		        time: "کات",
		        duration: "ماوە",
		        ipv4: "ناونیشانی IPv4",
		        ipv6: "ناونیشانی IPv6",
		        mac: "ناونیشانی MAC",
		        cidrv4: "مەودای IPv4",
		        cidrv6: "مەودای IPv6",
		        base64: "دەقی base64",
		        base64url: "دەقی base64url",
		        json_string: "دەقی JSON",
		        e164: "ژمارەی E.164",
		        credit_card: "ژمارەی کارتی کرێدیت",
		        currency_code: "کۆدی دراو",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "تێکردە",
		    };
		    // type names: missing keys = do not translate (use raw value via ?? fallback)
		    const TypeDictionary = {
		        nan: "NaN",
		        string: "نووسین",
		        number: "ژمارە",
		        boolean: "boolean",
		        array: "array",
		        object: "object",
		        date: "ڕێکەوت",
		        integer: "ژمارە",
		        float: "ژمارە",
		        null: "null",
		        undefined: "undefined",
		        function: "function",
		        symbol: "symbol",
		        unknown: "unknown",
		        promise: "promise",
		        void: "void",
		        never: "never",
		        map: "map",
		        set: "set",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                const postfix = ["ا", "و", "ۆ", "وو", "ە", "ی", "ێ"].some((p) => received.endsWith(p)) ? "یە" : "ە";
		                const isEnglish = /^[a-zA-Z]+$/.test(received);
		                if (receivedType === "null" || receivedType === "undefined")
		                    return `داواکراوە`;
		                return `چاوەڕوانکراوە ${expected} بێت، بەڵام ${received}${isEnglish ? "" : postfix}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `بەهاکە نادرووستە: چاوەڕوانکراوە ${util.stringifyPrimitive(issue.values[0])} بێت`;
		                return `هەڵبژاردەی نادروست: چاوەڕوانکراوە یەکێک بێت لە ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `پێویستە بە لایەنی زۆرەوە ${issue.maximum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `پێویستە بە لایەنی زۆرەوە ${issue.maximum.toString()} بێت`;
		            }
		            case "too_small": {
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `پێویستە بە لایەنی کەمەوە ${issue.minimum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `پێویستە بە لایەنی کەمەوە ${issue.minimum.toString()} بێت`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `دەقی نادروست: پێویستە دەستپێبکات بە "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `دەقی نادروست: پێویستە کۆتاییبێت بە "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `دەقی نادروست: پێویستە "${_issue.includes}" لەخۆبگرێت`;
		                if (_issue.format === "regex")
		                    return `دەقی نادروست: پێویستە لەگەڵ پاتێرنی ${_issue.pattern} بگونجێت`;
		                return `بەهای ${FormatDictionary[_issue.format] ?? issue.format} نادروستە`;
		            }
		            case "not_multiple_of":
		                return `ژمارەی نادروست: دەبێت چەند هێندە بێت بۆ ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `کلیلی نەناسراو: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `کلیلی نادروست لە ${issue.origin}`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `بەهای نەناسراو هەیە. بەهای چاوەڕوانکراو: ${opts}`;
		                }
		                return "یەکگرتنی نادروست";
		            case "invalid_element":
		                return `${issue.origin} بەهاکە نادروستە`;
		            default:
		                return `تێکردەی نادروست`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ckb$1, ckb$1.exports));
	return ckb$1.exports;
}

var cs$2 = {exports: {}};

var cs$1 = cs$2.exports;

var hasRequiredCs;

function requireCs () {
	if (hasRequiredCs) return cs$2.exports;
	hasRequiredCs = 1;
	(function (module, exports) {
		var __createBinding = (cs$1 && cs$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (cs$1 && cs$1.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (cs$1 && cs$1.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "znaků", verb: "mít" },
		        file: { unit: "bajtů", verb: "mít" },
		        array: { unit: "prvků", verb: "mít" },
		        set: { unit: "prvků", verb: "mít" },
		        map: { unit: "prvků", verb: "mít" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "regulární výraz",
		        email: "e-mailová adresa",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "datum a čas ve formátu ISO",
		        date: "datum ve formátu ISO",
		        time: "čas ve formátu ISO",
		        duration: "doba trvání ISO",
		        ipv4: "IPv4 adresa",
		        ipv6: "IPv6 adresa",
		        mac: "MAC adresa",
		        cidrv4: "rozsah IPv4",
		        cidrv6: "rozsah IPv6",
		        base64: "řetězec zakódovaný ve formátu base64",
		        base64url: "řetězec zakódovaný ve formátu base64url",
		        json_string: "řetězec ve formátu JSON",
		        e164: "číslo E.164",
		        credit_card: "číslo kreditní karty",
		        currency_code: "kód měny",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "vstup",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "číslo",
		        string: "řetězec",
		        function: "funkce",
		        array: "pole",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Neplatný vstup: očekáváno instanceof ${issue.expected}, obdrženo ${received}`;
		                }
		                return `Neplatný vstup: očekáváno ${expected}, obdrženo ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Neplatný vstup: očekáváno ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Neplatná možnost: očekávána jedna z hodnot ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Hodnota je příliš velká: ${issue.origin ?? "hodnota"} musí mít ${adj}${issue.maximum.toString()} ${sizing.unit ?? "prvků"}`;
		                }
		                return `Hodnota je příliš velká: ${issue.origin ?? "hodnota"} musí být ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Hodnota je příliš malá: ${issue.origin ?? "hodnota"} musí mít ${adj}${issue.minimum.toString()} ${sizing.unit ?? "prvků"}`;
		                }
		                return `Hodnota je příliš malá: ${issue.origin ?? "hodnota"} musí být ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Neplatný řetězec: musí začínat na "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Neplatný řetězec: musí končit na "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Neplatný řetězec: musí obsahovat "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Neplatný řetězec: musí odpovídat vzoru ${_issue.pattern}`;
		                return `Neplatný formát ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Neplatné číslo: musí být násobkem ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Neznámé klíče: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Neplatný klíč v ${issue.origin}`;
		            case "invalid_union":
		                return "Neplatný vstup";
		            case "invalid_element":
		                return `Neplatná hodnota v ${issue.origin}`;
		            default:
		                return `Neplatný vstup`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (cs$2, cs$2.exports));
	return cs$2.exports;
}

var da$1 = {exports: {}};

var da = da$1.exports;

var hasRequiredDa;

function requireDa () {
	if (hasRequiredDa) return da$1.exports;
	hasRequiredDa = 1;
	(function (module, exports) {
		var __createBinding = (da && da.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (da && da.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (da && da.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "tegn", verb: "havde" },
		        file: { unit: "bytes", verb: "havde" },
		        array: { unit: "elementer", verb: "indeholdt" },
		        set: { unit: "elementer", verb: "indeholdt" },
		        map: { unit: "elementer", verb: "indeholdt" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "e-mailadresse",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO dato- og klokkeslæt",
		        date: "ISO-dato",
		        time: "ISO-klokkeslæt",
		        duration: "ISO-varighed",
		        ipv4: "IPv4-adresse",
		        ipv6: "IPv6-adresse",
		        mac: "MAC-adresse",
		        cidrv4: "IPv4-spektrum",
		        cidrv6: "IPv6-spektrum",
		        base64: "base64-kodet streng",
		        base64url: "base64url-kodet streng",
		        json_string: "JSON-streng",
		        e164: "E.164-nummer",
		        credit_card: "kreditkortnummer",
		        currency_code: "valutakode",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        string: "streng",
		        number: "tal",
		        boolean: "boolean",
		        array: "liste",
		        object: "objekt",
		        set: "sæt",
		        file: "fil",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ugyldigt input: forventede instanceof ${issue.expected}, fik ${received}`;
		                }
		                return `Ugyldigt input: forventede ${expected}, fik ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ugyldig værdi: forventede ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ugyldigt valg: forventede en af følgende ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                if (sizing)
		                    return `For stor: forventede ${origin ?? "value"} ${sizing.verb} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "elementer"}`;
		                return `For stor: forventede ${origin ?? "value"} havde ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                if (sizing) {
		                    return `For lille: forventede ${origin} ${sizing.verb} ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `For lille: forventede ${origin} havde ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Ugyldig streng: skal starte med "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Ugyldig streng: skal ende med "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Ugyldig streng: skal indeholde "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Ugyldig streng: skal matche mønsteret ${_issue.pattern}`;
		                return `Ugyldig ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Ugyldigt tal: skal være deleligt med ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Ukendte nøgler" : "Ukendt nøgle"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Ugyldig nøgle i ${issue.origin}`;
		            case "invalid_union":
		                return "Ugyldigt input: matcher ingen af de tilladte typer";
		            case "invalid_element":
		                return `Ugyldig værdi i ${issue.origin}`;
		            default:
		                return `Ugyldigt input`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (da$1, da$1.exports));
	return da$1.exports;
}

var de$1 = {exports: {}};

var de = de$1.exports;

var hasRequiredDe;

function requireDe () {
	if (hasRequiredDe) return de$1.exports;
	hasRequiredDe = 1;
	(function (module, exports) {
		var __createBinding = (de && de.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (de && de.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (de && de.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "Zeichen", verb: "zu haben" },
		        file: { unit: "Bytes", verb: "zu haben" },
		        array: { unit: "Elemente", verb: "zu haben" },
		        set: { unit: "Elemente", verb: "zu haben" },
		        map: { unit: "Elemente", verb: "zu haben" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "Eingabe",
		        email: "E-Mail-Adresse",
		        url: "URL",
		        emoji: "Emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO-Datum und -Uhrzeit",
		        date: "ISO-Datum",
		        time: "ISO-Uhrzeit",
		        duration: "ISO-Dauer",
		        ipv4: "IPv4-Adresse",
		        ipv6: "IPv6-Adresse",
		        mac: "MAC-Adresse",
		        cidrv4: "IPv4-Bereich",
		        cidrv6: "IPv6-Bereich",
		        base64: "Base64-codierter String",
		        base64url: "Base64-URL-codierter String",
		        json_string: "JSON-String",
		        e164: "E.164-Nummer",
		        credit_card: "Kreditkartennummer",
		        currency_code: "Währungscode",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "Eingabe",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "Zahl",
		        array: "Array",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ungültige Eingabe: erwartet instanceof ${issue.expected}, erhalten ${received}`;
		                }
		                return `Ungültige Eingabe: erwartet ${expected}, erhalten ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ungültige Eingabe: erwartet ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ungültige Option: erwartet eine von ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Zu groß: erwartet, dass ${issue.origin ?? "Wert"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "Elemente"} hat`;
		                return `Zu groß: erwartet, dass ${issue.origin ?? "Wert"} ${adj}${issue.maximum.toString()} ist`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Zu klein: erwartet, dass ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} hat`;
		                }
		                return `Zu klein: erwartet, dass ${issue.origin} ${adj}${issue.minimum.toString()} ist`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Ungültiger String: muss mit "${_issue.prefix}" beginnen`;
		                if (_issue.format === "ends_with")
		                    return `Ungültiger String: muss mit "${_issue.suffix}" enden`;
		                if (_issue.format === "includes")
		                    return `Ungültiger String: muss "${_issue.includes}" enthalten`;
		                if (_issue.format === "regex")
		                    return `Ungültiger String: muss dem Muster ${_issue.pattern} entsprechen`;
		                return `Ungültig: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Ungültige Zahl: muss ein Vielfaches von ${issue.divisor} sein`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Unbekannte Schlüssel" : "Unbekannter Schlüssel"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Ungültiger Schlüssel in ${issue.origin}`;
		            case "invalid_union":
		                return "Ungültige Eingabe";
		            case "invalid_element":
		                return `Ungültiger Wert in ${issue.origin}`;
		            default:
		                return `Ungültige Eingabe`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (de$1, de$1.exports));
	return de$1.exports;
}

var el$1 = {exports: {}};

var el = el$1.exports;

var hasRequiredEl;

function requireEl () {
	if (hasRequiredEl) return el$1.exports;
	hasRequiredEl = 1;
	(function (module, exports) {
		var __createBinding = (el && el.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (el && el.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (el && el.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "χαρακτήρες", verb: "να έχει" },
		        file: { unit: "bytes", verb: "να έχει" },
		        array: { unit: "στοιχεία", verb: "να έχει" },
		        set: { unit: "στοιχεία", verb: "να έχει" },
		        map: { unit: "καταχωρήσεις", verb: "να έχει" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "είσοδος",
		        email: "διεύθυνση email",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO ημερομηνία και ώρα",
		        date: "ISO ημερομηνία",
		        time: "ISO ώρα",
		        duration: "ISO διάρκεια",
		        ipv4: "διεύθυνση IPv4",
		        ipv6: "διεύθυνση IPv6",
		        mac: "διεύθυνση MAC",
		        cidrv4: "εύρος IPv4",
		        cidrv6: "εύρος IPv6",
		        base64: "συμβολοσειρά κωδικοποιημένη σε base64",
		        base64url: "συμβολοσειρά κωδικοποιημένη σε base64url",
		        json_string: "συμβολοσειρά JSON",
		        e164: "αριθμός E.164",
		        credit_card: "αριθμός πιστωτικής κάρτας",
		        currency_code: "κωδικός νομίσματος",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "είσοδος",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (typeof issue.expected === "string" && /^[A-Z]/.test(issue.expected)) {
		                    return `Μη έγκυρη είσοδος: αναμενόταν instanceof ${issue.expected}, λήφθηκε ${received}`;
		                }
		                return `Μη έγκυρη είσοδος: αναμενόταν ${expected}, λήφθηκε ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Μη έγκυρη είσοδος: αναμενόταν ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Μη έγκυρη επιλογή: αναμενόταν ένα από ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Πολύ μεγάλο: αναμενόταν ${issue.origin ?? "τιμή"} να έχει ${adj}${issue.maximum.toString()} ${sizing.unit ?? "στοιχεία"}`;
		                return `Πολύ μεγάλο: αναμενόταν ${issue.origin ?? "τιμή"} να είναι ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Πολύ μικρό: αναμενόταν ${issue.origin} να έχει ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Πολύ μικρό: αναμενόταν ${issue.origin} να είναι ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Μη έγκυρη συμβολοσειρά: πρέπει να ξεκινά με "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Μη έγκυρη συμβολοσειρά: πρέπει να τελειώνει με "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Μη έγκυρη συμβολοσειρά: πρέπει να περιέχει "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Μη έγκυρη συμβολοσειρά: πρέπει να ταιριάζει με το μοτίβο ${_issue.pattern}`;
		                return `Μη έγκυρο: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Μη έγκυρος αριθμός: πρέπει να είναι πολλαπλάσιο του ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Άγνωστ${issue.keys.length > 1 ? "α" : "ο"} κλειδ${issue.keys.length > 1 ? "ιά" : "ί"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Μη έγκυρο κλειδί στο ${issue.origin}`;
		            case "invalid_union":
		                return "Μη έγκυρη είσοδος";
		            case "invalid_element":
		                return `Μη έγκυρη τιμή στο ${issue.origin}`;
		            default:
		                return `Μη έγκυρη είσοδος`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (el$1, el$1.exports));
	return el$1.exports;
}

var en$1 = {exports: {}};

var en = en$1.exports;

var hasRequiredEn;

function requireEn () {
	if (hasRequiredEn) return en$1.exports;
	hasRequiredEn = 1;
	(function (module, exports) {
		var __createBinding = (en && en.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (en && en.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (en && en.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "characters", verb: "to have" },
		        file: { unit: "bytes", verb: "to have" },
		        array: { unit: "items", verb: "to have" },
		        set: { unit: "items", verb: "to have" },
		        map: { unit: "entries", verb: "to have" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "email address",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO datetime",
		        date: "ISO date",
		        time: "ISO time",
		        duration: "ISO duration",
		        ipv4: "IPv4 address",
		        ipv6: "IPv6 address",
		        mac: "MAC address",
		        cidrv4: "IPv4 range",
		        cidrv6: "IPv6 range",
		        base64: "base64-encoded string",
		        base64url: "base64url-encoded string",
		        json_string: "JSON string",
		        e164: "E.164 number",
		        currency_code: "currency code",
		        credit_card: "credit card number",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    // type names: missing keys = do not translate (use raw value via ?? fallback)
		    const TypeDictionary = {
		        // Compatibility: "nan" -> "NaN" for display
		        nan: "NaN",
		        // All other type names omitted - they fall back to raw values via ?? operator
		    };
		    function getTypeName(type, input) {
		        if (type === "number" && typeof input === "number" && !Number.isFinite(input)) {
		            return String(input);
		        }
		        return TypeDictionary[type] ?? type;
		    }
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = getTypeName(issue.expected);
		                const receivedType = util.parsedType(issue.input);
		                const received = getTypeName(receivedType, issue.input);
		                return `Invalid input: expected ${expected}, received ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Invalid input: expected ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Invalid option: expected one of ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.exact ? "exactly " : issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Too big: expected ${issue.origin ?? "value"} to have ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elements"}`;
		                return `Too big: expected ${issue.origin ?? "value"} to be ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.exact ? "exactly " : issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Too small: expected ${issue.origin} to have ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Too small: expected ${issue.origin} to be ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Invalid string: must start with "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Invalid string: must end with "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Invalid string: must include "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Invalid string: must match pattern ${_issue.pattern}`;
		                return `Invalid ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Invalid number: must be a multiple of ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Unrecognized key${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Invalid key in ${issue.origin}`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `Invalid discriminator value. Expected ${opts}`;
		                }
		                if (issue.inclusive === false) {
		                    return "Invalid input: more than one option matched";
		                }
		                return "Invalid input";
		            case "invalid_element":
		                return `Invalid value in ${issue.origin}`;
		            default:
		                return `Invalid input`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (en$1, en$1.exports));
	return en$1.exports;
}

var eo$1 = {exports: {}};

var eo = eo$1.exports;

var hasRequiredEo;

function requireEo () {
	if (hasRequiredEo) return eo$1.exports;
	hasRequiredEo = 1;
	(function (module, exports) {
		var __createBinding = (eo && eo.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (eo && eo.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (eo && eo.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "karaktrojn", verb: "havi" },
		        file: { unit: "bajtojn", verb: "havi" },
		        array: { unit: "elementojn", verb: "havi" },
		        set: { unit: "elementojn", verb: "havi" },
		        map: { unit: "elementojn", verb: "havi" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "enigo",
		        email: "retadreso",
		        url: "URL",
		        emoji: "emoĝio",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO-datotempo",
		        date: "ISO-dato",
		        time: "ISO-tempo",
		        duration: "ISO-daŭro",
		        ipv4: "IPv4-adreso",
		        ipv6: "IPv6-adreso",
		        mac: "MAC-adreso",
		        cidrv4: "IPv4-rango",
		        cidrv6: "IPv6-rango",
		        base64: "64-ume kodita karaktraro",
		        base64url: "URL-64-ume kodita karaktraro",
		        json_string: "JSON-karaktraro",
		        e164: "E.164-nombro",
		        credit_card: "kreditkarta numero",
		        currency_code: "valuta kodo",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "enigo",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "nombro",
		        array: "tabelo",
		        null: "senvalora",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Nevalida enigo: atendiĝis instanceof ${issue.expected}, riceviĝis ${received}`;
		                }
		                return `Nevalida enigo: atendiĝis ${expected}, riceviĝis ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Nevalida enigo: atendiĝis ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Nevalida opcio: atendiĝis unu el ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Tro granda: atendiĝis ke ${issue.origin ?? "valoro"} havu ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementojn"}`;
		                return `Tro granda: atendiĝis ke ${issue.origin ?? "valoro"} havu ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Tro malgranda: atendiĝis ke ${issue.origin} havu ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Tro malgranda: atendiĝis ke ${issue.origin} estu ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Nevalida karaktraro: devas komenciĝi per "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Nevalida karaktraro: devas finiĝi per "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Nevalida karaktraro: devas inkluzivi "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Nevalida karaktraro: devas kongrui kun la modelo ${_issue.pattern}`;
		                return `Nevalida ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Nevalida nombro: devas esti oblo de ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Nekonata${issue.keys.length > 1 ? "j" : ""} ŝlosilo${issue.keys.length > 1 ? "j" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Nevalida ŝlosilo en ${issue.origin}`;
		            case "invalid_union":
		                return "Nevalida enigo";
		            case "invalid_element":
		                return `Nevalida valoro en ${issue.origin}`;
		            default:
		                return `Nevalida enigo`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (eo$1, eo$1.exports));
	return eo$1.exports;
}

var es$1 = {exports: {}};

var es = es$1.exports;

var hasRequiredEs;

function requireEs () {
	if (hasRequiredEs) return es$1.exports;
	hasRequiredEs = 1;
	(function (module, exports) {
		var __createBinding = (es && es.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (es && es.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (es && es.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caracteres", verb: "tener" },
		        file: { unit: "bytes", verb: "tener" },
		        array: { unit: "elementos", verb: "tener" },
		        set: { unit: "elementos", verb: "tener" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "entrada",
		        email: "dirección de correo electrónico",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "fecha y hora ISO",
		        date: "fecha ISO",
		        time: "hora ISO",
		        duration: "duración ISO",
		        ipv4: "dirección IPv4",
		        ipv6: "dirección IPv6",
		        mac: "dirección MAC",
		        cidrv4: "rango IPv4",
		        cidrv6: "rango IPv6",
		        base64: "cadena codificada en base64",
		        base64url: "URL codificada en base64",
		        json_string: "cadena JSON",
		        e164: "número E.164",
		        credit_card: "número de tarjeta de crédito",
		        currency_code: "código de moneda",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "entrada",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        string: "texto",
		        number: "número",
		        boolean: "booleano",
		        array: "arreglo",
		        object: "objeto",
		        set: "conjunto",
		        file: "archivo",
		        date: "fecha",
		        bigint: "número grande",
		        symbol: "símbolo",
		        undefined: "indefinido",
		        null: "nulo",
		        function: "función",
		        map: "mapa",
		        record: "registro",
		        tuple: "tupla",
		        enum: "enumeración",
		        union: "unión",
		        literal: "literal",
		        promise: "promesa",
		        void: "vacío",
		        never: "nunca",
		        unknown: "desconocido",
		        any: "cualquiera",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Entrada inválida: se esperaba instanceof ${issue.expected}, recibido ${received}`;
		                }
		                return `Entrada inválida: se esperaba ${expected}, recibido ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Entrada inválida: se esperaba ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Opción inválida: se esperaba una de ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                if (sizing)
		                    return `Demasiado grande: se esperaba que ${origin ?? "valor"} tuviera ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementos"}`;
		                return `Demasiado grande: se esperaba que ${origin ?? "valor"} fuera ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                if (sizing) {
		                    return `Demasiado pequeño: se esperaba que ${origin} tuviera ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Demasiado pequeño: se esperaba que ${origin} fuera ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Cadena inválida: debe comenzar con "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Cadena inválida: debe terminar en "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Cadena inválida: debe incluir "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Cadena inválida: debe coincidir con el patrón ${_issue.pattern}`;
		                return `Inválido ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Número inválido: debe ser múltiplo de ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Llave${issue.keys.length > 1 ? "s" : ""} desconocida${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Llave inválida en ${TypeDictionary[issue.origin] ?? issue.origin}`;
		            case "invalid_union":
		                return "Entrada inválida";
		            case "invalid_element":
		                return `Valor inválido en ${TypeDictionary[issue.origin] ?? issue.origin}`;
		            default:
		                return `Entrada inválida`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (es$1, es$1.exports));
	return es$1.exports;
}

var fa$1 = {exports: {}};

var fa = fa$1.exports;

var hasRequiredFa;

function requireFa () {
	if (hasRequiredFa) return fa$1.exports;
	hasRequiredFa = 1;
	(function (module, exports) {
		var __createBinding = (fa && fa.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (fa && fa.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (fa && fa.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "کاراکتر", verb: "داشته باشد" },
		        file: { unit: "بایت", verb: "داشته باشد" },
		        array: { unit: "آیتم", verb: "داشته باشد" },
		        set: { unit: "آیتم", verb: "داشته باشد" },
		        map: { unit: "آیتم", verb: "داشته باشد" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ورودی",
		        email: "آدرس ایمیل",
		        url: "URL",
		        emoji: "ایموجی",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "تاریخ و زمان ایزو",
		        date: "تاریخ ایزو",
		        time: "زمان ایزو",
		        duration: "مدت زمان ایزو",
		        ipv4: "IPv4 آدرس",
		        ipv6: "IPv6 آدرس",
		        mac: "MAC آدرس",
		        cidrv4: "IPv4 دامنه",
		        cidrv6: "IPv6 دامنه",
		        base64: "base64-encoded رشته",
		        base64url: "base64url-encoded رشته",
		        json_string: "JSON رشته",
		        e164: "E.164 عدد",
		        credit_card: "شماره کارت اعتباری",
		        currency_code: "کد ارز",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ورودی",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "عدد",
		        array: "آرایه",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `ورودی نامعتبر: می‌بایست instanceof ${issue.expected} می‌بود، ${received} دریافت شد`;
		                }
		                return `ورودی نامعتبر: می‌بایست ${expected} می‌بود، ${received} دریافت شد`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1) {
		                    return `ورودی نامعتبر: می‌بایست ${util.stringifyPrimitive(issue.values[0])} می‌بود`;
		                }
		                return `گزینه نامعتبر: می‌بایست یکی از ${util.joinValues(issue.values, "|")} می‌بود`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `خیلی بزرگ: ${issue.origin ?? "مقدار"} باید ${adj}${issue.maximum.toString()} ${sizing.unit ?? "عنصر"} باشد`;
		                }
		                return `خیلی بزرگ: ${issue.origin ?? "مقدار"} باید ${adj}${issue.maximum.toString()} باشد`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `خیلی کوچک: ${issue.origin} باید ${adj}${issue.minimum.toString()} ${sizing.unit} باشد`;
		                }
		                return `خیلی کوچک: ${issue.origin} باید ${adj}${issue.minimum.toString()} باشد`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `رشته نامعتبر: باید با "${_issue.prefix}" شروع شود`;
		                }
		                if (_issue.format === "ends_with") {
		                    return `رشته نامعتبر: باید با "${_issue.suffix}" تمام شود`;
		                }
		                if (_issue.format === "includes") {
		                    return `رشته نامعتبر: باید شامل "${_issue.includes}" باشد`;
		                }
		                if (_issue.format === "regex") {
		                    return `رشته نامعتبر: باید با الگوی ${_issue.pattern} مطابقت داشته باشد`;
		                }
		                return `${FormatDictionary[_issue.format] ?? issue.format} نامعتبر`;
		            }
		            case "not_multiple_of":
		                return `عدد نامعتبر: باید مضرب ${issue.divisor} باشد`;
		            case "unrecognized_keys":
		                return `کلید${issue.keys.length > 1 ? "های" : ""} ناشناس: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `کلید ناشناس در ${issue.origin}`;
		            case "invalid_union":
		                return `ورودی نامعتبر`;
		            case "invalid_element":
		                return `مقدار نامعتبر در ${issue.origin}`;
		            default:
		                return `ورودی نامعتبر`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (fa$1, fa$1.exports));
	return fa$1.exports;
}

var fi$1 = {exports: {}};

var fi = fi$1.exports;

var hasRequiredFi;

function requireFi () {
	if (hasRequiredFi) return fi$1.exports;
	hasRequiredFi = 1;
	(function (module, exports) {
		var __createBinding = (fi && fi.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (fi && fi.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (fi && fi.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "merkkiä", subject: "merkkijonon" },
		        file: { unit: "tavua", subject: "tiedoston" },
		        array: { unit: "alkiota", subject: "listan" },
		        set: { unit: "alkiota", subject: "joukon" },
		        map: { unit: "alkiota", subject: "kuvauksen" },
		        number: { unit: "", subject: "luvun" },
		        bigint: { unit: "", subject: "suuren kokonaisluvun" },
		        int: { unit: "", subject: "kokonaisluvun" },
		        date: { unit: "", subject: "päivämäärän" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "säännöllinen lauseke",
		        email: "sähköpostiosoite",
		        url: "URL-osoite",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO-aikaleima",
		        date: "ISO-päivämäärä",
		        time: "ISO-aika",
		        duration: "ISO-kesto",
		        ipv4: "IPv4-osoite",
		        ipv6: "IPv6-osoite",
		        mac: "MAC-osoite",
		        cidrv4: "IPv4-alue",
		        cidrv6: "IPv6-alue",
		        base64: "base64-koodattu merkkijono",
		        base64url: "base64url-koodattu merkkijono",
		        json_string: "JSON-merkkijono",
		        e164: "E.164-luku",
		        credit_card: "luottokortin numero",
		        currency_code: "valuuttakoodi",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "templaattimerkkijono",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Virheellinen tyyppi: odotettiin instanceof ${issue.expected}, oli ${received}`;
		                }
		                return `Virheellinen tyyppi: odotettiin ${expected}, oli ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Virheellinen syöte: täytyy olla ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Virheellinen valinta: täytyy olla yksi seuraavista: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Liian suuri: ${sizing.subject} täytyy olla ${adj}${issue.maximum.toString()} ${sizing.unit}`.trim();
		                }
		                return `Liian suuri: arvon täytyy olla ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Liian pieni: ${sizing.subject} täytyy olla ${adj}${issue.minimum.toString()} ${sizing.unit}`.trim();
		                }
		                return `Liian pieni: arvon täytyy olla ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Virheellinen syöte: täytyy alkaa "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Virheellinen syöte: täytyy loppua "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Virheellinen syöte: täytyy sisältää "${_issue.includes}"`;
		                if (_issue.format === "regex") {
		                    return `Virheellinen syöte: täytyy vastata säännöllistä lauseketta ${_issue.pattern}`;
		                }
		                return `Virheellinen ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Virheellinen luku: täytyy olla luvun ${issue.divisor} monikerta`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Tuntemattomat avaimet" : "Tuntematon avain"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return "Virheellinen avain tietueessa";
		            case "invalid_union":
		                return "Virheellinen unioni";
		            case "invalid_element":
		                return "Virheellinen arvo joukossa";
		            default:
		                return `Virheellinen syöte`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (fi$1, fi$1.exports));
	return fi$1.exports;
}

var fr$1 = {exports: {}};

var fr = fr$1.exports;

var hasRequiredFr;

function requireFr () {
	if (hasRequiredFr) return fr$1.exports;
	hasRequiredFr = 1;
	(function (module, exports) {
		var __createBinding = (fr && fr.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (fr && fr.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (fr && fr.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caractères", verb: "avoir" },
		        file: { unit: "octets", verb: "avoir" },
		        array: { unit: "éléments", verb: "avoir" },
		        set: { unit: "éléments", verb: "avoir" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "expression régulière",
		        email: "adresse e-mail",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "date et heure ISO",
		        date: "date ISO",
		        time: "heure ISO",
		        duration: "durée ISO",
		        ipv4: "adresse IPv4",
		        ipv6: "adresse IPv6",
		        mac: "adresse MAC",
		        cidrv4: "plage IPv4",
		        cidrv6: "plage IPv6",
		        base64: "chaîne de caractères encodée en base64",
		        base64url: "chaîne de caractères encodée en base64url",
		        json_string: "chaîne de caractères JSON",
		        e164: "numéro au format E.164",
		        credit_card: "numéro de carte de crédit",
		        currency_code: "code de devise",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "entrée",
		    };
		    const TypeDictionary = {
		        string: "chaîne de caractères",
		        number: "nombre",
		        int: "entier",
		        boolean: "booléen",
		        bigint: "grand entier",
		        symbol: "symbole",
		        undefined: "indéfini",
		        null: "null",
		        never: "jamais",
		        void: "vide",
		        date: "date",
		        array: "tableau",
		        object: "objet",
		        tuple: "tuple",
		        record: "record",
		        map: "map",
		        set: "ensemble",
		        file: "fichier",
		        nonoptional: "non optionnel",
		        nan: "NaN",
		        function: "fonction",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Entrée invalide : instance de ${issue.expected} attendu, ${received} reçu`;
		                }
		                return `Entrée invalide : ${expected} attendu, ${received} reçu`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Entrée invalide : ${util.stringifyPrimitive(issue.values[0])} attendu`;
		                return `Option invalide : une valeur parmi ${util.joinValues(issue.values, "|")} attendue`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Trop grand : ${TypeDictionary[issue.origin] ?? "valeur"} doit ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "élément(s)"}`;
		                return `Trop grand : ${TypeDictionary[issue.origin] ?? "valeur"} doit être ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Trop petit : ${TypeDictionary[issue.origin] ?? "valeur"} doit ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                return `Trop petit : ${TypeDictionary[issue.origin] ?? "valeur"} doit être ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Chaîne de caractères invalide : doit commencer par "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Chaîne de caractères invalide : doit se terminer par "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Chaîne de caractères invalide : doit inclure "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Chaîne de caractères invalide : doit correspondre au motif ${_issue.pattern}`;
		                return `${FormatDictionary[_issue.format] ?? issue.format} invalide`;
		            }
		            case "not_multiple_of":
		                return `Nombre invalide : doit être un multiple de ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Clé${issue.keys.length > 1 ? "s" : ""} non reconnue${issue.keys.length > 1 ? "s" : ""} : ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Clé invalide dans ${issue.origin}`;
		            case "invalid_union":
		                return "Entrée invalide";
		            case "invalid_element":
		                return `Valeur invalide dans ${issue.origin}`;
		            default:
		                return `Entrée invalide`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (fr$1, fr$1.exports));
	return fr$1.exports;
}

var frCA$1 = {exports: {}};

var frCA = frCA$1.exports;

var hasRequiredFrCA;

function requireFrCA () {
	if (hasRequiredFrCA) return frCA$1.exports;
	hasRequiredFrCA = 1;
	(function (module, exports) {
		var __createBinding = (frCA && frCA.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (frCA && frCA.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (frCA && frCA.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caractères", verb: "avoir" },
		        file: { unit: "octets", verb: "avoir" },
		        array: { unit: "éléments", verb: "avoir" },
		        set: { unit: "éléments", verb: "avoir" },
		        map: { unit: "éléments", verb: "avoir" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "entrée",
		        email: "adresse courriel",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "date-heure ISO",
		        date: "date ISO",
		        time: "heure ISO",
		        duration: "durée ISO",
		        ipv4: "adresse IPv4",
		        ipv6: "adresse IPv6",
		        mac: "adresse MAC",
		        cidrv4: "plage IPv4",
		        cidrv6: "plage IPv6",
		        base64: "chaîne encodée en base64",
		        base64url: "chaîne encodée en base64url",
		        json_string: "chaîne JSON",
		        e164: "numéro E.164",
		        credit_card: "numéro de carte de crédit",
		        currency_code: "code de devise",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "entrée",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Entrée invalide : attendu instanceof ${issue.expected}, reçu ${received}`;
		                }
		                return `Entrée invalide : attendu ${expected}, reçu ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Entrée invalide : attendu ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Option invalide : attendu l'une des valeurs suivantes ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "≤" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Trop grand : attendu que ${issue.origin ?? "la valeur"} ait ${adj}${issue.maximum.toString()} ${sizing.unit}`;
		                return `Trop grand : attendu que ${issue.origin ?? "la valeur"} soit ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? "≥" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Trop petit : attendu que ${issue.origin} ait ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Trop petit : attendu que ${issue.origin} soit ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Chaîne invalide : doit commencer par "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Chaîne invalide : doit se terminer par "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Chaîne invalide : doit inclure "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Chaîne invalide : doit correspondre au motif ${_issue.pattern}`;
		                return `${FormatDictionary[_issue.format] ?? issue.format} invalide`;
		            }
		            case "not_multiple_of":
		                return `Nombre invalide : doit être un multiple de ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Clé${issue.keys.length > 1 ? "s" : ""} non reconnue${issue.keys.length > 1 ? "s" : ""} : ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Clé invalide dans ${issue.origin}`;
		            case "invalid_union":
		                return "Entrée invalide";
		            case "invalid_element":
		                return `Valeur invalide dans ${issue.origin}`;
		            default:
		                return `Entrée invalide`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (frCA$1, frCA$1.exports));
	return frCA$1.exports;
}

var gu$1 = {exports: {}};

var gu = gu$1.exports;

var hasRequiredGu;

function requireGu () {
	if (hasRequiredGu) return gu$1.exports;
	hasRequiredGu = 1;
	(function (module, exports) {
		var __createBinding = (gu && gu.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (gu && gu.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (gu && gu.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "અક્ષર", verb: "હોવા જોઈએ" },
		        file: { unit: "બાયટ", verb: "હોવા જોઈએ" },
		        array: { unit: "આઇટમ", verb: "હોવા જોઈએ" },
		        set: { unit: "આઇટમ", verb: "હોવા જોઈએ" },
		        map: { unit: "એન્ટ્રી", verb: "હોવા જોઈએ" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ઇનપુટ",
		        email: "ઈમેઇલ એડ્રેસ",
		        url: "URL",
		        emoji: "ઇમોજી",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO તારીખ અને સમય",
		        date: "ISO તારીખ",
		        time: "ISO સમય",
		        duration: "ISO અવધિ",
		        ipv4: "IPv4 એડ્રેસ",
		        ipv6: "IPv6 એડ્રેસ",
		        mac: "MAC એડ્રેસ",
		        cidrv4: "IPv4 શ્રેણી",
		        cidrv6: "IPv6 શ્રેણી",
		        base64: "base64-એન્કોડેડ સ્ટ્રિંગ",
		        base64url: "base64url-એન્કોડેડ સ્ટ્રિંગ",
		        json_string: "JSON સ્ટ્રિંગ",
		        e164: "E.164 નંબર",
		        credit_card: "ક્રેડિટ કાર્ડ નંબર",
		        currency_code: "ચલણ કોડ",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ઇનપુટ",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `અમાન્ય ઇનપુટ: અપેક્ષિત ${expected}, પ્રાપ્ત ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `અમાન્ય ઇનપુટ: અપેક્ષિત ${util.stringifyPrimitive(issue.values[0])}`;
		                return `અમાન્ય વિકલ્પ: ${util.joinValues(issue.values, " | ")} માધ્યમથી એક અપેક્ષિત`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `ખૂબ મોટું: ${issue.origin ?? "મૂલ્ય"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "એલિમેન્ટ"} હોવા જોઈએ`;
		                return `ખૂબ મોટું: ${issue.origin ?? "મૂલ્ય"} ${adj}${issue.maximum.toString()} હોવું જોઈએ`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `ખૂબ નાનું: ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} હોવા જોઈએ`;
		                }
		                return `ખૂબ નાનું: ${issue.origin} ${adj}${issue.minimum.toString()} હોવું જોઈએ`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `અમાન્ય સ્ટ્રિંગ: "${_issue.prefix}" થી શરૂ થવું જોઈએ`;
		                }
		                if (_issue.format === "ends_with")
		                    return `અમાન્ય સ્ટ્રિંગ: "${_issue.suffix}" પર સમાપ્ત થવું જોઈએ`;
		                if (_issue.format === "includes")
		                    return `અમાન્ય સ્ટ્રિંગ: "${_issue.includes}" શામેલ હોવું જોઈએ`;
		                if (_issue.format === "regex")
		                    return `અમાન્ય સ્ટ્રિંગ: પેટર્ન ${_issue.pattern} સાથે મેળ ખાવું જોઈએ`;
		                return `અમાન્ય ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `અમાન્ય નંબર: ${issue.divisor} નો ગુણાંક હોવો જોઈએ`;
		            case "unrecognized_keys":
		                return `ઓળખી શકાતા નહીં તે કી${issue.keys.length > 1 ? "ઓ" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} માં અમાન્ય કી`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `અમાન્ય ડિસ્ક્રિમિનેટર મૂલ્ય. અપેક્ષિત ${opts}`;
		                }
		                return "અમાન્ય ઇનપુટ";
		            case "invalid_element":
		                return `${issue.origin} માં અમાન્ય મૂલ્ય`;
		            default:
		                return "અમાન્ય ઇનપુટ";
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (gu$1, gu$1.exports));
	return gu$1.exports;
}

var he$1 = {exports: {}};

var he = he$1.exports;

var hasRequiredHe;

function requireHe () {
	if (hasRequiredHe) return he$1.exports;
	hasRequiredHe = 1;
	(function (module, exports) {
		var __createBinding = (he && he.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (he && he.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (he && he.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    // Hebrew labels + grammatical gender
		    const TypeNames = {
		        string: { label: "מחרוזת", gender: "f" },
		        number: { label: "מספר", gender: "m" },
		        boolean: { label: "ערך בוליאני", gender: "m" },
		        bigint: { label: "BigInt", gender: "m" },
		        date: { label: "תאריך", gender: "m" },
		        array: { label: "מערך", gender: "m" },
		        object: { label: "אובייקט", gender: "m" },
		        null: { label: "ערך ריק (null)", gender: "m" },
		        undefined: { label: "ערך לא מוגדר (undefined)", gender: "m" },
		        symbol: { label: "סימבול (Symbol)", gender: "m" },
		        function: { label: "פונקציה", gender: "f" },
		        map: { label: "מפה (Map)", gender: "f" },
		        set: { label: "קבוצה (Set)", gender: "f" },
		        file: { label: "קובץ", gender: "m" },
		        promise: { label: "Promise", gender: "m" },
		        NaN: { label: "NaN", gender: "m" },
		        unknown: { label: "ערך לא ידוע", gender: "m" },
		        value: { label: "ערך", gender: "m" },
		    };
		    // Sizing units for size-related messages + localized origin labels
		    const Sizable = {
		        string: { unit: "תווים", shortLabel: "קצר", longLabel: "ארוך" },
		        file: { unit: "בייטים", shortLabel: "קטן", longLabel: "גדול" },
		        array: { unit: "פריטים", shortLabel: "קטן", longLabel: "גדול" },
		        set: { unit: "פריטים", shortLabel: "קטן", longLabel: "גדול" },
		        number: { unit: "", shortLabel: "קטן", longLabel: "גדול" }, // no unit
		    };
		    // Helpers — labels, articles, and verbs
		    const typeEntry = (t) => (t ? TypeNames[t] : undefined);
		    const typeLabel = (t) => {
		        const e = typeEntry(t);
		        if (e)
		            return e.label;
		        // fallback: show raw string if unknown
		        return t ?? TypeNames.unknown.label;
		    };
		    const withDefinite = (t) => `ה${typeLabel(t)}`;
		    const verbFor = (t) => {
		        const e = typeEntry(t);
		        const gender = e?.gender ?? "m";
		        return gender === "f" ? "צריכה להיות" : "צריך להיות";
		    };
		    const getSizing = (origin) => {
		        if (!origin)
		            return null;
		        return Sizable[origin] ?? null;
		    };
		    const FormatDictionary = {
		        regex: { label: "קלט", gender: "m" },
		        email: { label: "כתובת אימייל", gender: "f" },
		        url: { label: "כתובת רשת", gender: "f" },
		        emoji: { label: "אימוג'י", gender: "m" },
		        uuid: { label: "UUID", gender: "m" },
		        uuidv4: { label: "UUIDv4", gender: "m" },
		        uuidv6: { label: "UUIDv6", gender: "m" },
		        nanoid: { label: "nanoid", gender: "m" },
		        guid: { label: "GUID", gender: "m" },
		        cuid: { label: "cuid", gender: "m" },
		        cuid2: { label: "cuid2", gender: "m" },
		        ulid: { label: "ULID", gender: "m" },
		        xid: { label: "XID", gender: "m" },
		        ksuid: { label: "KSUID", gender: "m" },
		        datetime: { label: "תאריך וזמן ISO", gender: "m" },
		        date: { label: "תאריך ISO", gender: "m" },
		        time: { label: "זמן ISO", gender: "m" },
		        duration: { label: "משך זמן ISO", gender: "m" },
		        ipv4: { label: "כתובת IPv4", gender: "f" },
		        ipv6: { label: "כתובת IPv6", gender: "f" },
		        mac: { label: "כתובת MAC", gender: "f" },
		        cidrv4: { label: "טווח IPv4", gender: "m" },
		        cidrv6: { label: "טווח IPv6", gender: "m" },
		        base64: { label: "מחרוזת בבסיס 64", gender: "f" },
		        base64url: { label: "מחרוזת בבסיס 64 לכתובות רשת", gender: "f" },
		        json_string: { label: "מחרוזת JSON", gender: "f" },
		        e164: { label: "מספר E.164", gender: "m" },
		        credit_card: { label: "מספר כרטיס אשראי", gender: "m" },
		        currency_code: { label: "קוד מטבע", gender: "m" },
		        iban: { label: "IBAN", gender: "m" },
		        jwt: { label: "JWT", gender: "m" },
		        template_literal: { label: "קלט", gender: "m" },
		        ends_with: { label: "קלט", gender: "m" },
		        includes: { label: "קלט", gender: "m" },
		        lowercase: { label: "קלט", gender: "m" },
		        starts_with: { label: "קלט", gender: "m" },
		        uppercase: { label: "קלט", gender: "m" },
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                // Expected type: show without definite article for clearer Hebrew
		                const expectedKey = issue.expected;
		                const expected = TypeDictionary[expectedKey ?? ""] ?? typeLabel(expectedKey);
		                // Received: show localized label if known, otherwise constructor/raw
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? TypeNames[receivedType]?.label ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `קלט לא תקין: צריך להיות instanceof ${issue.expected}, התקבל ${received}`;
		                }
		                return `קלט לא תקין: צריך להיות ${expected}, התקבל ${received}`;
		            }
		            case "invalid_value": {
		                if (issue.values.length === 1) {
		                    return `ערך לא תקין: הערך חייב להיות ${util.stringifyPrimitive(issue.values[0])}`;
		                }
		                // Join values with proper Hebrew formatting
		                const stringified = issue.values.map((v) => util.stringifyPrimitive(v));
		                if (issue.values.length === 2) {
		                    return `ערך לא תקין: האפשרויות המתאימות הן ${stringified[0]} או ${stringified[1]}`;
		                }
		                // For 3+ values: "a", "b" או "c"
		                const lastValue = stringified[stringified.length - 1];
		                const restValues = stringified.slice(0, -1).join(", ");
		                return `ערך לא תקין: האפשרויות המתאימות הן ${restValues} או ${lastValue}`;
		            }
		            case "too_big": {
		                const sizing = getSizing(issue.origin);
		                const subject = withDefinite(issue.origin ?? "value");
		                if (issue.origin === "string") {
		                    // Special handling for strings - more natural Hebrew
		                    return `${sizing?.longLabel ?? "ארוך"} מדי: ${subject} צריכה להכיל ${issue.maximum.toString()} ${sizing?.unit ?? ""} ${issue.inclusive ? "או פחות" : "לכל היותר"}`.trim();
		                }
		                if (issue.origin === "number") {
		                    // Natural Hebrew for numbers
		                    const comparison = issue.inclusive ? `קטן או שווה ל-${issue.maximum}` : `קטן מ-${issue.maximum}`;
		                    return `גדול מדי: ${subject} צריך להיות ${comparison}`;
		                }
		                if (issue.origin === "array" || issue.origin === "set") {
		                    // Natural Hebrew for arrays and sets
		                    const verb = issue.origin === "set" ? "צריכה" : "צריך";
		                    const comparison = issue.inclusive
		                        ? `${issue.maximum} ${sizing?.unit ?? ""} או פחות`
		                        : `פחות מ-${issue.maximum} ${sizing?.unit ?? ""}`;
		                    return `גדול מדי: ${subject} ${verb} להכיל ${comparison}`.trim();
		                }
		                const adj = issue.inclusive ? "<=" : "<";
		                const be = verbFor(issue.origin ?? "value");
		                if (sizing?.unit) {
		                    return `${sizing.longLabel} מדי: ${subject} ${be} ${adj}${issue.maximum.toString()} ${sizing.unit}`;
		                }
		                return `${sizing?.longLabel ?? "גדול"} מדי: ${subject} ${be} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const sizing = getSizing(issue.origin);
		                const subject = withDefinite(issue.origin ?? "value");
		                if (issue.origin === "string") {
		                    // Special handling for strings - more natural Hebrew
		                    return `${sizing?.shortLabel ?? "קצר"} מדי: ${subject} צריכה להכיל ${issue.minimum.toString()} ${sizing?.unit ?? ""} ${issue.inclusive ? "או יותר" : "לפחות"}`.trim();
		                }
		                if (issue.origin === "number") {
		                    // Natural Hebrew for numbers
		                    const comparison = issue.inclusive ? `גדול או שווה ל-${issue.minimum}` : `גדול מ-${issue.minimum}`;
		                    return `קטן מדי: ${subject} צריך להיות ${comparison}`;
		                }
		                if (issue.origin === "array" || issue.origin === "set") {
		                    // Natural Hebrew for arrays and sets
		                    const verb = issue.origin === "set" ? "צריכה" : "צריך";
		                    // Special case for singular (minimum === 1)
		                    if (issue.minimum === 1 && issue.inclusive) {
		                        const singularPhrase = issue.origin === "set" ? "לפחות פריט אחד" : "לפחות פריט אחד";
		                        return `קטן מדי: ${subject} ${verb} להכיל ${singularPhrase}`;
		                    }
		                    const comparison = issue.inclusive
		                        ? `${issue.minimum} ${sizing?.unit ?? ""} או יותר`
		                        : `יותר מ-${issue.minimum} ${sizing?.unit ?? ""}`;
		                    return `קטן מדי: ${subject} ${verb} להכיל ${comparison}`.trim();
		                }
		                const adj = issue.inclusive ? ">=" : ">";
		                const be = verbFor(issue.origin ?? "value");
		                if (sizing?.unit) {
		                    return `${sizing.shortLabel} מדי: ${subject} ${be} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `${sizing?.shortLabel ?? "קטן"} מדי: ${subject} ${be} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                // These apply to strings — use feminine grammar + ה׳ הידיעה
		                if (_issue.format === "starts_with")
		                    return `המחרוזת חייבת להתחיל ב "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `המחרוזת חייבת להסתיים ב "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `המחרוזת חייבת לכלול "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `המחרוזת חייבת להתאים לתבנית ${_issue.pattern}`;
		                // Handle gender agreement for formats
		                const nounEntry = FormatDictionary[_issue.format];
		                const noun = nounEntry?.label ?? _issue.format;
		                const gender = nounEntry?.gender ?? "m";
		                const adjective = gender === "f" ? "תקינה" : "תקין";
		                return `${noun} לא ${adjective}`;
		            }
		            case "not_multiple_of":
		                return `מספר לא תקין: חייב להיות מכפלה של ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `מפתח${issue.keys.length > 1 ? "ות" : ""} לא מזוה${issue.keys.length > 1 ? "ים" : "ה"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key": {
		                return `שדה לא תקין באובייקט`;
		            }
		            case "invalid_union":
		                return "קלט לא תקין";
		            case "invalid_element": {
		                const place = withDefinite(issue.origin ?? "array");
		                return `ערך לא תקין ב${place}`;
		            }
		            default:
		                return `קלט לא תקין`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (he$1, he$1.exports));
	return he$1.exports;
}

var hi$1 = {exports: {}};

var hi = hi$1.exports;

var hasRequiredHi;

function requireHi () {
	if (hasRequiredHi) return hi$1.exports;
	hasRequiredHi = 1;
	(function (module, exports) {
		var __createBinding = (hi && hi.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (hi && hi.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (hi && hi.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "अक्षर", verb: "रखने के लिए" },
		        file: { unit: "बाइट्स", verb: "रखने के लिए" },
		        array: { unit: "तत्व", verb: "रखने के लिए" },
		        set: { unit: "तत्व", verb: "रखने के लिए" },
		        map: { unit: "प्रविष्टियाँ", verb: "रखने के लिए" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "इनपुट",
		        email: "ईमेल पता",
		        url: "URL",
		        emoji: "इमोजी",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO तिथि और समय",
		        date: "ISO तिथि",
		        time: "ISO समय",
		        duration: "ISO अवधि",
		        ipv4: "IPv4 पता",
		        ipv6: "IPv6 पता",
		        mac: "MAC पता",
		        cidrv4: "IPv4 श्रेणी",
		        cidrv6: "IPv6 श्रेणी",
		        base64: "Base64-एन्कोडेड स्ट्रिंग",
		        base64url: "Base64URL-एन्कोडेड स्ट्रिंग",
		        json_string: "JSON स्ट्रिंग",
		        e164: "E.164 संख्या",
		        credit_card: "क्रेडिट कार्ड संख्या",
		        currency_code: "मुद्रा कोड",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "इनपुट",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `अमान्य इनपुट: अपेक्षित ${expected}, प्राप्त ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `अमान्य इनपुट: अपेक्षित ${util.stringifyPrimitive(issue.values[0])}`;
		                return `अमान्य विकल्प: अपेक्षित मानों में से एक ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `बहुत बड़ा: अपेक्षित था कि ${issue.origin ?? "मान"} में ${adj}${issue.maximum} ${sizing.unit} हों`;
		                return `बहुत बड़ा: अपेक्षित था कि ${issue.origin ?? "मान"} ${adj}${issue.maximum} हो`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `बहुत छोटा: अपेक्षित था कि ${issue.origin} में ${adj}${issue.minimum} ${sizing.unit} हों`;
		                return `बहुत छोटा: अपेक्षित था कि ${issue.origin} ${adj}${issue.minimum} हो`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `अमान्य स्ट्रिंग: "${_issue.prefix}" से शुरू होना चाहिए`;
		                if (_issue.format === "ends_with")
		                    return `अमान्य स्ट्रिंग: "${_issue.suffix}" पर समाप्त होना चाहिए`;
		                if (_issue.format === "includes")
		                    return `अमान्य स्ट्रिंग: इसमें "${_issue.includes}" शामिल होना चाहिए`;
		                if (_issue.format === "regex")
		                    return `अमान्य स्ट्रिंग: पैटर्न ${_issue.pattern} से मेल खाना चाहिए`;
		                return `अमान्य ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `अमान्य संख्या: यह ${issue.divisor} का गुणज होना चाहिए`;
		            case "unrecognized_keys":
		                return `अपरिचित कुंजी${issue.keys.length > 1 ? "याँ" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `अमान्य कुंजी: ${issue.origin} में`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `अमान्य डिस्क्रिमिनेटर मान: अपेक्षित ${opts}`;
		                }
		                return "अमान्य इनपुट";
		            case "invalid_element":
		                return `अमान्य मान: ${issue.origin} में`;
		            default:
		                return `अमान्य इनपुट`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (hi$1, hi$1.exports));
	return hi$1.exports;
}

var hr$1 = {exports: {}};

var hr = hr$1.exports;

var hasRequiredHr;

function requireHr () {
	if (hasRequiredHr) return hr$1.exports;
	hasRequiredHr = 1;
	(function (module, exports) {
		var __createBinding = (hr && hr.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (hr && hr.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (hr && hr.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "znakova", verb: "imati" },
		        file: { unit: "bajtova", verb: "imati" },
		        array: { unit: "stavki", verb: "imati" },
		        set: { unit: "stavki", verb: "imati" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "unos",
		        email: "email adresa",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO datum i vrijeme",
		        date: "ISO datum",
		        time: "ISO vrijeme",
		        duration: "ISO trajanje",
		        ipv4: "IPv4 adresa",
		        ipv6: "IPv6 adresa",
		        mac: "MAC adresa",
		        cidrv4: "IPv4 raspon",
		        cidrv6: "IPv6 raspon",
		        base64: "base64 kodirani tekst",
		        base64url: "base64url kodirani tekst",
		        json_string: "JSON tekst",
		        e164: "E.164 broj",
		        credit_card: "broj kreditne kartice",
		        currency_code: "kod valute",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "unos",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        string: "tekst",
		        number: "broj",
		        boolean: "boolean",
		        array: "niz",
		        object: "objekt",
		        set: "skup",
		        file: "datoteka",
		        date: "datum",
		        bigint: "bigint",
		        symbol: "simbol",
		        undefined: "undefined",
		        null: "null",
		        function: "funkcija",
		        map: "mapa",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Neispravan unos: očekuje se instanceof ${issue.expected}, a primljeno je ${received}`;
		                }
		                return `Neispravan unos: očekuje se ${expected}, a primljeno je ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Neispravna vrijednost: očekivano ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Neispravna opcija: očekivano jedno od ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                if (sizing)
		                    return `Preveliko: očekivano da ${origin ?? "vrijednost"} ima ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elemenata"}`;
		                return `Preveliko: očekivano da ${origin ?? "vrijednost"} bude ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                if (sizing) {
		                    return `Premalo: očekivano da ${origin} ima ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Premalo: očekivano da ${origin} bude ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Neispravan tekst: mora započinjati s "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Neispravan tekst: mora završavati s "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Neispravan tekst: mora sadržavati "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Neispravan tekst: mora odgovarati uzorku ${_issue.pattern}`;
		                return `Neispravna ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Neispravan broj: mora biti višekratnik od ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Neprepoznat${issue.keys.length > 1 ? "i ključevi" : " ključ"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Neispravan ključ u ${TypeDictionary[issue.origin] ?? issue.origin}`;
		            case "invalid_union":
		                return "Neispravan unos";
		            case "invalid_element":
		                return `Neispravna vrijednost u ${TypeDictionary[issue.origin] ?? issue.origin}`;
		            default:
		                return `Neispravan unos`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (hr$1, hr$1.exports));
	return hr$1.exports;
}

var hu$1 = {exports: {}};

var hu = hu$1.exports;

var hasRequiredHu;

function requireHu () {
	if (hasRequiredHu) return hu$1.exports;
	hasRequiredHu = 1;
	(function (module, exports) {
		var __createBinding = (hu && hu.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (hu && hu.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (hu && hu.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "karakter", verb: "legyen" },
		        file: { unit: "byte", verb: "legyen" },
		        array: { unit: "elem", verb: "legyen" },
		        set: { unit: "elem", verb: "legyen" },
		        map: { unit: "elem", verb: "legyen" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "bemenet",
		        email: "email cím",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO időbélyeg",
		        date: "ISO dátum",
		        time: "ISO idő",
		        duration: "ISO időintervallum",
		        ipv4: "IPv4 cím",
		        ipv6: "IPv6 cím",
		        mac: "MAC cím",
		        cidrv4: "IPv4 tartomány",
		        cidrv6: "IPv6 tartomány",
		        base64: "base64-kódolt string",
		        base64url: "base64url-kódolt string",
		        json_string: "JSON string",
		        e164: "E.164 szám",
		        credit_card: "hitelkártyaszám",
		        currency_code: "pénznemkód",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "bemenet",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "szám",
		        array: "tömb",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Érvénytelen bemenet: a várt érték instanceof ${issue.expected}, a kapott érték ${received}`;
		                }
		                return `Érvénytelen bemenet: a várt érték ${expected}, a kapott érték ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Érvénytelen bemenet: a várt érték ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Érvénytelen opció: valamelyik érték várt ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Túl nagy: ${issue.origin ?? "érték"} mérete túl nagy ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elem"}`;
		                return `Túl nagy: a bemeneti érték ${issue.origin ?? "érték"} túl nagy: ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Túl kicsi: a bemeneti érték ${issue.origin} mérete túl kicsi ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Túl kicsi: a bemeneti érték ${issue.origin} túl kicsi ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Érvénytelen string: "${_issue.prefix}" értékkel kell kezdődnie`;
		                if (_issue.format === "ends_with")
		                    return `Érvénytelen string: "${_issue.suffix}" értékkel kell végződnie`;
		                if (_issue.format === "includes")
		                    return `Érvénytelen string: "${_issue.includes}" értéket kell tartalmaznia`;
		                if (_issue.format === "regex")
		                    return `Érvénytelen string: ${_issue.pattern} mintának kell megfelelnie`;
		                return `Érvénytelen ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Érvénytelen szám: ${issue.divisor} többszörösének kell lennie`;
		            case "unrecognized_keys":
		                return `Ismeretlen kulcs${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Érvénytelen kulcs ${issue.origin}`;
		            case "invalid_union":
		                return "Érvénytelen bemenet";
		            case "invalid_element":
		                return `Érvénytelen érték: ${issue.origin}`;
		            default:
		                return `Érvénytelen bemenet`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (hu$1, hu$1.exports));
	return hu$1.exports;
}

var hy$1 = {exports: {}};

var hy = hy$1.exports;

var hasRequiredHy;

function requireHy () {
	if (hasRequiredHy) return hy$1.exports;
	hasRequiredHy = 1;
	(function (module, exports) {
		var __createBinding = (hy && hy.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (hy && hy.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (hy && hy.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		function getArmenianPlural(count, one, many) {
		    return Math.abs(count) === 1 ? one : many;
		}
		function withDefiniteArticle(word) {
		    if (!word)
		        return "";
		    const vowels = ["ա", "ե", "ը", "ի", "ո", "ու", "օ"];
		    const lastChar = word[word.length - 1];
		    return word + (vowels.includes(lastChar) ? "ն" : "ը");
		}
		const error = () => {
		    const Sizable = {
		        string: {
		            unit: {
		                one: "նշան",
		                many: "նշաններ",
		            },
		            verb: "ունենալ",
		        },
		        file: {
		            unit: {
		                one: "բայթ",
		                many: "բայթեր",
		            },
		            verb: "ունենալ",
		        },
		        array: {
		            unit: {
		                one: "տարր",
		                many: "տարրեր",
		            },
		            verb: "ունենալ",
		        },
		        set: {
		            unit: {
		                one: "տարր",
		                many: "տարրեր",
		            },
		            verb: "ունենալ",
		        },
		        map: {
		            unit: {
		                one: "տարր",
		                many: "տարրեր",
		            },
		            verb: "ունենալ",
		        },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "մուտք",
		        email: "էլ. հասցե",
		        url: "URL",
		        emoji: "էմոջի",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO ամսաթիվ և ժամ",
		        date: "ISO ամսաթիվ",
		        time: "ISO ժամ",
		        duration: "ISO տևողություն",
		        ipv4: "IPv4 հասցե",
		        ipv6: "IPv6 հասցե",
		        mac: "MAC հասցե",
		        cidrv4: "IPv4 միջակայք",
		        cidrv6: "IPv6 միջակայք",
		        base64: "base64 ձևաչափով տող",
		        base64url: "base64url ձևաչափով տող",
		        json_string: "JSON տող",
		        e164: "E.164 համար",
		        credit_card: "կրեդիտ քարտի համար",
		        currency_code: "արժույթի կոդ",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "մուտք",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "թիվ",
		        array: "զանգված",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Սխալ մուտքագրում․ սպասվում էր instanceof ${issue.expected}, ստացվել է ${received}`;
		                }
		                return `Սխալ մուտքագրում․ սպասվում էր ${expected}, ստացվել է ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Սխալ մուտքագրում․ սպասվում էր ${util.stringifyPrimitive(issue.values[1])}`;
		                return `Սխալ տարբերակ․ սպասվում էր հետևյալներից մեկը՝ ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    const maxValue = Number(issue.maximum);
		                    const unit = getArmenianPlural(maxValue, sizing.unit.one, sizing.unit.many);
		                    return `Չափազանց մեծ արժեք․ սպասվում է, որ ${withDefiniteArticle(issue.origin ?? "արժեք")} կունենա ${adj}${issue.maximum.toString()} ${unit}`;
		                }
		                return `Չափազանց մեծ արժեք․ սպասվում է, որ ${withDefiniteArticle(issue.origin ?? "արժեք")} լինի ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    const minValue = Number(issue.minimum);
		                    const unit = getArmenianPlural(minValue, sizing.unit.one, sizing.unit.many);
		                    return `Չափազանց փոքր արժեք․ սպասվում է, որ ${withDefiniteArticle(issue.origin)} կունենա ${adj}${issue.minimum.toString()} ${unit}`;
		                }
		                return `Չափազանց փոքր արժեք․ սպասվում է, որ ${withDefiniteArticle(issue.origin)} լինի ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Սխալ տող․ պետք է սկսվի "${_issue.prefix}"-ով`;
		                if (_issue.format === "ends_with")
		                    return `Սխալ տող․ պետք է ավարտվի "${_issue.suffix}"-ով`;
		                if (_issue.format === "includes")
		                    return `Սխալ տող․ պետք է պարունակի "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Սխալ տող․ պետք է համապատասխանի ${_issue.pattern} ձևաչափին`;
		                return `Սխալ ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Սխալ թիվ․ պետք է բազմապատիկ լինի ${issue.divisor}-ի`;
		            case "unrecognized_keys":
		                return `Չճանաչված բանալի${issue.keys.length > 1 ? "ներ" : ""}. ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Սխալ բանալի ${withDefiniteArticle(issue.origin)}-ում`;
		            case "invalid_union":
		                return "Սխալ մուտքագրում";
		            case "invalid_element":
		                return `Սխալ արժեք ${withDefiniteArticle(issue.origin)}-ում`;
		            default:
		                return `Սխալ մուտքագրում`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (hy$1, hy$1.exports));
	return hy$1.exports;
}

var id$1 = {exports: {}};

var id = id$1.exports;

var hasRequiredId;

function requireId () {
	if (hasRequiredId) return id$1.exports;
	hasRequiredId = 1;
	(function (module, exports) {
		var __createBinding = (id && id.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (id && id.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (id && id.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "karakter", verb: "memiliki" },
		        file: { unit: "byte", verb: "memiliki" },
		        array: { unit: "item", verb: "memiliki" },
		        set: { unit: "item", verb: "memiliki" },
		        map: { unit: "item", verb: "memiliki" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "alamat email",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "tanggal dan waktu format ISO",
		        date: "tanggal format ISO",
		        time: "jam format ISO",
		        duration: "durasi format ISO",
		        ipv4: "alamat IPv4",
		        ipv6: "alamat IPv6",
		        mac: "alamat MAC",
		        cidrv4: "rentang alamat IPv4",
		        cidrv6: "rentang alamat IPv6",
		        base64: "string dengan enkode base64",
		        base64url: "string dengan enkode base64url",
		        json_string: "string JSON",
		        e164: "angka E.164",
		        credit_card: "nomor kartu kredit",
		        currency_code: "kode mata uang",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Input tidak valid: diharapkan instanceof ${issue.expected}, diterima ${received}`;
		                }
		                return `Input tidak valid: diharapkan ${expected}, diterima ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Input tidak valid: diharapkan ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Pilihan tidak valid: diharapkan salah satu dari ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Terlalu besar: diharapkan ${issue.origin ?? "value"} memiliki ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elemen"}`;
		                return `Terlalu besar: diharapkan ${issue.origin ?? "value"} menjadi ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Terlalu kecil: diharapkan ${issue.origin} memiliki ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Terlalu kecil: diharapkan ${issue.origin} menjadi ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `String tidak valid: harus dimulai dengan "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `String tidak valid: harus berakhir dengan "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `String tidak valid: harus menyertakan "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `String tidak valid: harus sesuai pola ${_issue.pattern}`;
		                return `${FormatDictionary[_issue.format] ?? issue.format} tidak valid`;
		            }
		            case "not_multiple_of":
		                return `Angka tidak valid: harus kelipatan dari ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Kunci tidak dikenali ${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Kunci tidak valid di ${issue.origin}`;
		            case "invalid_union":
		                return "Input tidak valid";
		            case "invalid_element":
		                return `Nilai tidak valid di ${issue.origin}`;
		            default:
		                return `Input tidak valid`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (id$1, id$1.exports));
	return id$1.exports;
}

var is$2 = {exports: {}};

var is$1 = is$2.exports;

var hasRequiredIs$1;

function requireIs$1 () {
	if (hasRequiredIs$1) return is$2.exports;
	hasRequiredIs$1 = 1;
	(function (module, exports) {
		var __createBinding = (is$1 && is$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (is$1 && is$1.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (is$1 && is$1.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "stafi", verb: "að hafa" },
		        file: { unit: "bæti", verb: "að hafa" },
		        array: { unit: "hluti", verb: "að hafa" },
		        set: { unit: "hluti", verb: "að hafa" },
		        map: { unit: "hluti", verb: "að hafa" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "gildi",
		        email: "netfang",
		        url: "vefslóð",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO dagsetning og tími",
		        date: "ISO dagsetning",
		        time: "ISO tími",
		        duration: "ISO tímalengd",
		        ipv4: "IPv4 address",
		        ipv6: "IPv6 address",
		        mac: "MAC address",
		        cidrv4: "IPv4 range",
		        cidrv6: "IPv6 range",
		        base64: "base64-encoded strengur",
		        base64url: "base64url-encoded strengur",
		        json_string: "JSON strengur",
		        e164: "E.164 tölugildi",
		        credit_card: "kreditkortanúmer",
		        currency_code: "gjaldmiðilskóði",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "gildi",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "númer",
		        array: "fylki",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Rangt gildi: Þú slóst inn ${received} þar sem á að vera instanceof ${issue.expected}`;
		                }
		                return `Rangt gildi: Þú slóst inn ${received} þar sem á að vera ${expected}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Rangt gildi: gert ráð fyrir ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ógilt val: má vera eitt af eftirfarandi ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Of stórt: gert er ráð fyrir að ${issue.origin ?? "gildi"} hafi ${adj}${issue.maximum.toString()} ${sizing.unit ?? "hluti"}`;
		                return `Of stórt: gert er ráð fyrir að ${issue.origin ?? "gildi"} sé ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Of lítið: gert er ráð fyrir að ${issue.origin} hafi ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Of lítið: gert er ráð fyrir að ${issue.origin} sé ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Ógildur strengur: verður að byrja á "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Ógildur strengur: verður að enda á "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Ógildur strengur: verður að innihalda "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Ógildur strengur: verður að fylgja mynstri ${_issue.pattern}`;
		                return `Rangt ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Röng tala: verður að vera margfeldi af ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Óþekkt ${issue.keys.length > 1 ? "ir lyklar" : "ur lykill"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Rangur lykill í ${issue.origin}`;
		            case "invalid_union":
		                return "Rangt gildi";
		            case "invalid_element":
		                return `Rangt gildi í ${issue.origin}`;
		            default:
		                return `Rangt gildi`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (is$2, is$2.exports));
	return is$2.exports;
}

var it$1 = {exports: {}};

var it = it$1.exports;

var hasRequiredIt;

function requireIt () {
	if (hasRequiredIt) return it$1.exports;
	hasRequiredIt = 1;
	(function (module, exports) {
		var __createBinding = (it && it.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (it && it.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (it && it.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caratteri", verb: "avere" },
		        file: { unit: "byte", verb: "avere" },
		        array: { unit: "elementi", verb: "avere" },
		        set: { unit: "elementi", verb: "avere" },
		        map: { unit: "elementi", verb: "avere" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "indirizzo email",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "data e ora ISO",
		        date: "data ISO",
		        time: "ora ISO",
		        duration: "durata ISO",
		        ipv4: "indirizzo IPv4",
		        ipv6: "indirizzo IPv6",
		        mac: "indirizzo MAC",
		        cidrv4: "intervallo IPv4",
		        cidrv6: "intervallo IPv6",
		        base64: "stringa codificata in base64",
		        base64url: "URL codificata in base64",
		        json_string: "stringa JSON",
		        e164: "numero E.164",
		        credit_card: "numero di carta di credito",
		        currency_code: "codice valuta",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "numero",
		        array: "vettore",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Input non valido: atteso instanceof ${issue.expected}, ricevuto ${received}`;
		                }
		                return `Input non valido: atteso ${expected}, ricevuto ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Input non valido: atteso ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Opzione non valida: atteso uno tra ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Troppo grande: ${issue.origin ?? "valore"} deve avere ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementi"}`;
		                return `Troppo grande: ${issue.origin ?? "valore"} deve essere ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Troppo piccolo: ${issue.origin} deve avere ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Troppo piccolo: ${issue.origin} deve essere ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Stringa non valida: deve iniziare con "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Stringa non valida: deve terminare con "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Stringa non valida: deve includere "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Stringa non valida: deve corrispondere al pattern ${_issue.pattern}`;
		                return `Input non valido: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Numero non valido: deve essere un multiplo di ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Chiav${issue.keys.length > 1 ? "i" : "e"} non riconosciut${issue.keys.length > 1 ? "e" : "a"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Chiave non valida in ${issue.origin}`;
		            case "invalid_union":
		                return "Input non valido";
		            case "invalid_element":
		                return `Valore non valido in ${issue.origin}`;
		            default:
		                return `Input non valido`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (it$1, it$1.exports));
	return it$1.exports;
}

var ja$1 = {exports: {}};

var ja = ja$1.exports;

var hasRequiredJa;

function requireJa () {
	if (hasRequiredJa) return ja$1.exports;
	hasRequiredJa = 1;
	(function (module, exports) {
		var __createBinding = (ja && ja.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ja && ja.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ja && ja.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "文字", verb: "である" },
		        file: { unit: "バイト", verb: "である" },
		        array: { unit: "要素", verb: "である" },
		        set: { unit: "要素", verb: "である" },
		        map: { unit: "要素", verb: "である" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "入力値",
		        email: "メールアドレス",
		        url: "URL",
		        emoji: "絵文字",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO日時",
		        date: "ISO日付",
		        time: "ISO時刻",
		        duration: "ISO期間",
		        ipv4: "IPv4アドレス",
		        ipv6: "IPv6アドレス",
		        mac: "MACアドレス",
		        cidrv4: "IPv4範囲",
		        cidrv6: "IPv6範囲",
		        base64: "base64エンコード文字列",
		        base64url: "base64urlエンコード文字列",
		        json_string: "JSON文字列",
		        e164: "E.164番号",
		        credit_card: "クレジットカード番号",
		        currency_code: "通貨コード",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "入力値",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "数値",
		        array: "配列",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `無効な入力: instanceof ${issue.expected}が期待されましたが、${received}が入力されました`;
		                }
		                return `無効な入力: ${expected}が期待されましたが、${received}が入力されました`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `無効な入力: ${util.stringifyPrimitive(issue.values[0])}が期待されました`;
		                return `無効な選択: ${util.joinValues(issue.values, "、")}のいずれかである必要があります`;
		            case "too_big": {
		                const adj = issue.inclusive ? "以下である" : "より小さい";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `大きすぎる値: ${issue.origin ?? "値"}は${issue.maximum.toString()}${sizing.unit ?? "要素"}${adj}必要があります`;
		                return `大きすぎる値: ${issue.origin ?? "値"}は${issue.maximum.toString()}${adj}必要があります`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? "以上である" : "より大きい";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `小さすぎる値: ${issue.origin}は${issue.minimum.toString()}${sizing.unit}${adj}必要があります`;
		                return `小さすぎる値: ${issue.origin}は${issue.minimum.toString()}${adj}必要があります`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `無効な文字列: "${_issue.prefix}"で始まる必要があります`;
		                if (_issue.format === "ends_with")
		                    return `無効な文字列: "${_issue.suffix}"で終わる必要があります`;
		                if (_issue.format === "includes")
		                    return `無効な文字列: "${_issue.includes}"を含む必要があります`;
		                if (_issue.format === "regex")
		                    return `無効な文字列: パターン${_issue.pattern}に一致する必要があります`;
		                return `無効な${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `無効な数値: ${issue.divisor}の倍数である必要があります`;
		            case "unrecognized_keys":
		                return `認識されていないキー${issue.keys.length > 1 ? "群" : ""}: ${util.joinValues(issue.keys, "、")}`;
		            case "invalid_key":
		                return `${issue.origin}内の無効なキー`;
		            case "invalid_union":
		                return "無効な入力";
		            case "invalid_element":
		                return `${issue.origin}内の無効な値`;
		            default:
		                return `無効な入力`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ja$1, ja$1.exports));
	return ja$1.exports;
}

var ka$1 = {exports: {}};

var ka = ka$1.exports;

var hasRequiredKa;

function requireKa () {
	if (hasRequiredKa) return ka$1.exports;
	hasRequiredKa = 1;
	(function (module, exports) {
		var __createBinding = (ka && ka.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ka && ka.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ka && ka.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "სიმბოლო", verb: "უნდა შეიცავდეს" },
		        file: { unit: "ბაიტი", verb: "უნდა შეიცავდეს" },
		        array: { unit: "ელემენტი", verb: "უნდა შეიცავდეს" },
		        set: { unit: "ელემენტი", verb: "უნდა შეიცავდეს" },
		        map: { unit: "ელემენტი", verb: "უნდა შეიცავდეს" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "შეყვანა",
		        email: "ელ-ფოსტის მისამართი",
		        url: "URL",
		        emoji: "ემოჯი",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "თარიღი-დრო",
		        date: "თარიღი",
		        time: "დრო",
		        duration: "ხანგრძლივობა",
		        ipv4: "IPv4 მისამართი",
		        ipv6: "IPv6 მისამართი",
		        mac: "MAC მისამართი",
		        cidrv4: "IPv4 დიაპაზონი",
		        cidrv6: "IPv6 დიაპაზონი",
		        base64: "base64-კოდირებული ველი",
		        base64url: "base64url-კოდირებული ველი",
		        json_string: "JSON ველი",
		        e164: "E.164 ნომერი",
		        credit_card: "საკრედიტო ბარათის ნომერი",
		        currency_code: "ვალუტის კოდი",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "შეყვანა",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "რიცხვი",
		        string: "ველი",
		        boolean: "ბულეანი",
		        function: "ფუნქცია",
		        array: "მასივი",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `არასწორი შეყვანა: მოსალოდნელი instanceof ${issue.expected}, მიღებული ${received}`;
		                }
		                return `არასწორი შეყვანა: მოსალოდნელი ${expected}, მიღებული ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `არასწორი შეყვანა: მოსალოდნელი ${util.stringifyPrimitive(issue.values[0])}`;
		                return `არასწორი ვარიანტი: მოსალოდნელია ერთ-ერთი ${util.joinValues(issue.values, "|")}-დან`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `ზედმეტად დიდი: მოსალოდნელი ${issue.origin ?? "მნიშვნელობა"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit}`;
		                return `ზედმეტად დიდი: მოსალოდნელი ${issue.origin ?? "მნიშვნელობა"} იყოს ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `ზედმეტად პატარა: მოსალოდნელი ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `ზედმეტად პატარა: მოსალოდნელი ${issue.origin} იყოს ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `არასწორი ველი: უნდა იწყებოდეს "${_issue.prefix}"-ით`;
		                }
		                if (_issue.format === "ends_with")
		                    return `არასწორი ველი: უნდა მთავრდებოდეს "${_issue.suffix}"-ით`;
		                if (_issue.format === "includes")
		                    return `არასწორი ველი: უნდა შეიცავდეს "${_issue.includes}"-ს`;
		                if (_issue.format === "regex")
		                    return `არასწორი ველი: უნდა შეესაბამებოდეს შაბლონს ${_issue.pattern}`;
		                return `არასწორი ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `არასწორი რიცხვი: უნდა იყოს ${issue.divisor}-ის ჯერადი`;
		            case "unrecognized_keys":
		                return `უცნობი გასაღებ${issue.keys.length > 1 ? "ები" : "ი"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `არასწორი გასაღები ${issue.origin}-ში`;
		            case "invalid_union":
		                return "არასწორი შეყვანა";
		            case "invalid_element":
		                return `არასწორი მნიშვნელობა ${issue.origin}-ში`;
		            default:
		                return `არასწორი შეყვანა`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ka$1, ka$1.exports));
	return ka$1.exports;
}

var kh$1 = {exports: {}};

var km$1 = {exports: {}};

var km = km$1.exports;

var hasRequiredKm;

function requireKm () {
	if (hasRequiredKm) return km$1.exports;
	hasRequiredKm = 1;
	(function (module, exports) {
		var __createBinding = (km && km.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (km && km.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (km && km.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "តួអក្សរ", verb: "គួរមាន" },
		        file: { unit: "បៃ", verb: "គួរមាន" },
		        array: { unit: "ធាតុ", verb: "គួរមាន" },
		        set: { unit: "ធាតុ", verb: "គួរមាន" },
		        map: { unit: "ធាតុ", verb: "គួរមាន" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ទិន្នន័យបញ្ចូល",
		        email: "អាសយដ្ឋានអ៊ីមែល",
		        url: "URL",
		        emoji: "សញ្ញាអារម្មណ៍",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "កាលបរិច្ឆេទ និងម៉ោង ISO",
		        date: "កាលបរិច្ឆេទ ISO",
		        time: "ម៉ោង ISO",
		        duration: "រយៈពេល ISO",
		        ipv4: "អាសយដ្ឋាន IPv4",
		        ipv6: "អាសយដ្ឋាន IPv6",
		        mac: "អាសយដ្ឋាន MAC",
		        cidrv4: "ដែនអាសយដ្ឋាន IPv4",
		        cidrv6: "ដែនអាសយដ្ឋាន IPv6",
		        base64: "ខ្សែអក្សរអ៊ិកូដ base64",
		        base64url: "ខ្សែអក្សរអ៊ិកូដ base64url",
		        json_string: "ខ្សែអក្សរ JSON",
		        e164: "លេខ E.164",
		        credit_card: "លេខប័ណ្ណឥណទាន",
		        currency_code: "កូដរូបិយប័ណ្ណ",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ទិន្នន័យបញ្ចូល",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "លេខ",
		        array: "អារេ (Array)",
		        null: "គ្មានតម្លៃ (null)",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `ទិន្នន័យបញ្ចូលមិនត្រឹមត្រូវ៖ ត្រូវការ instanceof ${issue.expected} ប៉ុន្តែទទួលបាន ${received}`;
		                }
		                return `ទិន្នន័យបញ្ចូលមិនត្រឹមត្រូវ៖ ត្រូវការ ${expected} ប៉ុន្តែទទួលបាន ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `ទិន្នន័យបញ្ចូលមិនត្រឹមត្រូវ៖ ត្រូវការ ${util.stringifyPrimitive(issue.values[0])}`;
		                return `ជម្រើសមិនត្រឹមត្រូវ៖ ត្រូវជាមួយក្នុងចំណោម ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `ធំពេក៖ ត្រូវការ ${issue.origin ?? "តម្លៃ"} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "ធាតុ"}`;
		                return `ធំពេក៖ ត្រូវការ ${issue.origin ?? "តម្លៃ"} ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `តូចពេក៖ ត្រូវការ ${issue.origin} ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `តូចពេក៖ ត្រូវការ ${issue.origin} ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវចាប់ផ្តើមដោយ "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវបញ្ចប់ដោយ "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវមាន "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `ខ្សែអក្សរមិនត្រឹមត្រូវ៖ ត្រូវតែផ្គូផ្គងនឹងទម្រង់ដែលបានកំណត់ ${_issue.pattern}`;
		                return `មិនត្រឹមត្រូវ៖ ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `លេខមិនត្រឹមត្រូវ៖ ត្រូវតែជាពហុគុណនៃ ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `រកឃើញសោមិនស្គាល់៖ ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `សោមិនត្រឹមត្រូវនៅក្នុង ${issue.origin}`;
		            case "invalid_union":
		                return `ទិន្នន័យមិនត្រឹមត្រូវ`;
		            case "invalid_element":
		                return `ទិន្នន័យមិនត្រឹមត្រូវនៅក្នុង ${issue.origin}`;
		            default:
		                return `ទិន្នន័យមិនត្រឹមត្រូវ`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (km$1, km$1.exports));
	return km$1.exports;
}

var kh = kh$1.exports;

var hasRequiredKh;

function requireKh () {
	if (hasRequiredKh) return kh$1.exports;
	hasRequiredKh = 1;
	(function (module, exports) {
		var __importDefault = (kh && kh.__importDefault) || function (mod) {
		    return (mod && mod.__esModule) ? mod : { "default": mod };
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const km_js_1 = __importDefault(/*@__PURE__*/ requireKm());
		/** @deprecated Use `km` instead. */
		function default_1() {
		    return (0, km_js_1.default)();
		}
		module.exports = exports.default; 
	} (kh$1, kh$1.exports));
	return kh$1.exports;
}

var kn$1 = {exports: {}};

var kn = kn$1.exports;

var hasRequiredKn;

function requireKn () {
	if (hasRequiredKn) return kn$1.exports;
	hasRequiredKn = 1;
	(function (module, exports) {
		var __createBinding = (kn && kn.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (kn && kn.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (kn && kn.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "ಅಕ್ಷರಗಳು", verb: "ಹೊಂದಲು" },
		        file: { unit: "ಬೈಟ್‌ಗಳು", verb: "ಹೊಂದಲು" },
		        array: { unit: "ವಸ್ತುಗಳು", verb: "ಹೊಂದಲು" },
		        set: { unit: "ವಸ್ತುಗಳು", verb: "ಹೊಂದಲು" },
		        map: { unit: "entries", verb: "ಹೊಂದಲು" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ಇನ್ಪುಟ್",
		        email: "email ವಿಳಾಸ",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO ದಿನಾಂಕದ ಸಮಯ",
		        date: "ISO ದಿನಾಂಕ",
		        time: "ISO ಸಮಯ",
		        duration: "ISO ಅವಧಿ",
		        ipv4: "IPv4 ವಿಳಾಸ",
		        ipv6: "IPv6 ವಿಳಾಸ",
		        mac: "MAC ವಿಳಾಸ",
		        cidrv4: "IPv4 ವ್ಯಾಪ್ತಿಯ",
		        cidrv6: "IPv6 ವ್ಯಾಪ್ತಿಯ",
		        base64: "base64-encodedಸ್ಟ್ರಿಂಗ್",
		        base64url: "base64url-encodedಸ್ಟ್ರಿಂಗ್",
		        json_string: "JSONಸ್ಟ್ರಿಂಗ್",
		        e164: "E.164 ಸಂಖ್ಯೆ",
		        credit_card: "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಸಂಖ್ಯೆ",
		        currency_code: "ಕರೆನ್ಸಿ ಕೋಡ್",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ಇನ್ಪುಟ್",
		    };
		    // type names: missing keys = do not translate (use raw value via ?? fallback)
		    const TypeDictionary = {
		        // Compatibility: "nan" -> "NaN" for display
		        nan: "NaN",
		        // All other type names omitted - they fall back to raw values via ?? operator
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `ಅಮಾನ್ಯ ಇನ್‌ಪುಟ್: ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${expected}, ಸ್ವೀಕರಿಸಿದನು ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `ಅಮಾನ್ಯ ಇನ್‌ಪುಟ್: ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${util.stringifyPrimitive(issue.values[0])}`;
		                return `ಅಮಾನ್ಯ ಆಯ್ಕೆ: ಇವುಗಳಲ್ಲಿ ಒಂದನ್ನು ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `ತುಂಬಾ ದೊಡ್ಡದು: ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${issue.origin ?? "value"} ಹೊಂದಲು ${adj}${issue.maximum.toString()} ${sizing.unit ?? "ಅಂಶಗಳು"}`;
		                return `ತುಂಬಾ ದೊಡ್ಡದು: ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${issue.origin ?? "value"} ಎಂದು ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `ತುಂಬಾ ಚಿಕ್ಕದು: ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${issue.origin} ಹೊಂದಲು ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `ತುಂಬಾ ಚಿಕ್ಕದು: ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${issue.origin} ಎಂದು ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `ಅಮಾನ್ಯವಾದ ಸ್ಟ್ರಿಂಗ್: ಇದರೊಂದಿಗೆ ಪ್ರಾರಂಭಿಸಬೇಕು "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `ಅಮಾನ್ಯವಾದ ಸ್ಟ್ರಿಂಗ್: ಇದರೊಂದಿಗೆ ಕೊನೆಗೊಳ್ಳಬೇಕು "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `ಅಮಾನ್ಯ ಸ್ಟ್ರಿಂಗ್: ಒಳಗೊಂಡಿರಬೇಕು "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `ಅಮಾನ್ಯವಾದ ಸ್ಟ್ರಿಂಗ್: ಮಾದರಿಗೆ ಹೊಂದಿಕೆಯಾಗಬೇಕು ${_issue.pattern}`;
		                return `ಅಮಾನ್ಯ ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `ಅಮಾನ್ಯ ಸಂಖ್ಯೆ: ಬಹುಸಂಖ್ಯೆಯಾಗಿರಬೇಕು ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `ಗುರುತಿಸಲಾಗದ ಕೀ ${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `ಅಮಾನ್ಯವಾದ ಕೀ ಇನ್ ${issue.origin}`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `ಅಮಾನ್ಯ ತಾರತಮ್ಯ ಮೌಲ್ಯ. ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ${opts}`;
		                }
		                return "ಅಮಾನ್ಯ ಇನ್‌ಪುಟ್";
		            case "invalid_element":
		                return `ರಲ್ಲಿ ಅಮಾನ್ಯ ಮೌಲ್ಯ ${issue.origin}`;
		            default:
		                return `ಅಮಾನ್ಯ ಇನ್‌ಪುಟ್`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (kn$1, kn$1.exports));
	return kn$1.exports;
}

var ko$1 = {exports: {}};

var ko = ko$1.exports;

var hasRequiredKo;

function requireKo () {
	if (hasRequiredKo) return ko$1.exports;
	hasRequiredKo = 1;
	(function (module, exports) {
		var __createBinding = (ko && ko.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ko && ko.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ko && ko.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "문자", verb: "to have" },
		        file: { unit: "바이트", verb: "to have" },
		        array: { unit: "개", verb: "to have" },
		        set: { unit: "개", verb: "to have" },
		        map: { unit: "개", verb: "to have" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "입력",
		        email: "이메일 주소",
		        url: "URL",
		        emoji: "이모지",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO 날짜시간",
		        date: "ISO 날짜",
		        time: "ISO 시간",
		        duration: "ISO 기간",
		        ipv4: "IPv4 주소",
		        ipv6: "IPv6 주소",
		        mac: "MAC 주소",
		        cidrv4: "IPv4 범위",
		        cidrv6: "IPv6 범위",
		        base64: "base64 인코딩 문자열",
		        base64url: "base64url 인코딩 문자열",
		        json_string: "JSON 문자열",
		        e164: "E.164 번호",
		        credit_card: "신용카드 번호",
		        currency_code: "통화 코드",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "입력",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `잘못된 입력: 예상 타입은 instanceof ${issue.expected}, 받은 타입은 ${received}입니다`;
		                }
		                return `잘못된 입력: 예상 타입은 ${expected}, 받은 타입은 ${received}입니다`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `잘못된 입력: 값은 ${util.stringifyPrimitive(issue.values[0])} 이어야 합니다`;
		                return `잘못된 옵션: ${util.joinValues(issue.values, "또는 ")} 중 하나여야 합니다`;
		            case "too_big": {
		                const adj = issue.inclusive ? "이하" : "미만";
		                const suffix = adj === "미만" ? "이어야 합니다" : "여야 합니다";
		                const sizing = getSizing(issue.origin);
		                const unit = sizing?.unit ?? "요소";
		                if (sizing)
		                    return `${issue.origin ?? "값"}이 너무 큽니다: ${issue.maximum.toString()}${unit} ${adj}${suffix}`;
		                return `${issue.origin ?? "값"}이 너무 큽니다: ${issue.maximum.toString()} ${adj}${suffix}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? "이상" : "초과";
		                const suffix = adj === "이상" ? "이어야 합니다" : "여야 합니다";
		                const sizing = getSizing(issue.origin);
		                const unit = sizing?.unit ?? "요소";
		                if (sizing) {
		                    return `${issue.origin ?? "값"}이 너무 작습니다: ${issue.minimum.toString()}${unit} ${adj}${suffix}`;
		                }
		                return `${issue.origin ?? "값"}이 너무 작습니다: ${issue.minimum.toString()} ${adj}${suffix}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `잘못된 문자열: "${_issue.prefix}"(으)로 시작해야 합니다`;
		                }
		                if (_issue.format === "ends_with")
		                    return `잘못된 문자열: "${_issue.suffix}"(으)로 끝나야 합니다`;
		                if (_issue.format === "includes")
		                    return `잘못된 문자열: "${_issue.includes}"을(를) 포함해야 합니다`;
		                if (_issue.format === "regex")
		                    return `잘못된 문자열: 정규식 ${_issue.pattern} 패턴과 일치해야 합니다`;
		                return `잘못된 ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `잘못된 숫자: ${issue.divisor}의 배수여야 합니다`;
		            case "unrecognized_keys":
		                return `인식할 수 없는 키: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `잘못된 키: ${issue.origin}`;
		            case "invalid_union":
		                return `잘못된 입력`;
		            case "invalid_element":
		                return `잘못된 값: ${issue.origin}`;
		            default:
		                return `잘못된 입력`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ko$1, ko$1.exports));
	return ko$1.exports;
}

var lt$1 = {exports: {}};

var lt = lt$1.exports;

var hasRequiredLt;

function requireLt () {
	if (hasRequiredLt) return lt$1.exports;
	hasRequiredLt = 1;
	(function (module, exports) {
		var __createBinding = (lt && lt.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (lt && lt.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (lt && lt.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const capitalizeFirstCharacter = (text) => {
		    return text.charAt(0).toUpperCase() + text.slice(1);
		};
		function getUnitTypeFromNumber(number) {
		    const abs = Math.abs(number);
		    const last = abs % 10;
		    const last2 = abs % 100;
		    if ((last2 >= 11 && last2 <= 19) || last === 0)
		        return "many";
		    if (last === 1)
		        return "one";
		    return "few";
		}
		const error = () => {
		    const Sizable = {
		        string: {
		            unit: {
		                one: "simbolis",
		                few: "simboliai",
		                many: "simbolių",
		            },
		            verb: {
		                smaller: {
		                    inclusive: "turi būti ne ilgesnė kaip",
		                    notInclusive: "turi būti trumpesnė kaip",
		                },
		                bigger: {
		                    inclusive: "turi būti ne trumpesnė kaip",
		                    notInclusive: "turi būti ilgesnė kaip",
		                },
		            },
		        },
		        file: {
		            unit: {
		                one: "baitas",
		                few: "baitai",
		                many: "baitų",
		            },
		            verb: {
		                smaller: {
		                    inclusive: "turi būti ne didesnis kaip",
		                    notInclusive: "turi būti mažesnis kaip",
		                },
		                bigger: {
		                    inclusive: "turi būti ne mažesnis kaip",
		                    notInclusive: "turi būti didesnis kaip",
		                },
		            },
		        },
		        array: {
		            unit: {
		                one: "elementą",
		                few: "elementus",
		                many: "elementų",
		            },
		            verb: {
		                smaller: {
		                    inclusive: "turi turėti ne daugiau kaip",
		                    notInclusive: "turi turėti mažiau kaip",
		                },
		                bigger: {
		                    inclusive: "turi turėti ne mažiau kaip",
		                    notInclusive: "turi turėti daugiau kaip",
		                },
		            },
		        },
		        set: {
		            unit: {
		                one: "elementą",
		                few: "elementus",
		                many: "elementų",
		            },
		            verb: {
		                smaller: {
		                    inclusive: "turi turėti ne daugiau kaip",
		                    notInclusive: "turi turėti mažiau kaip",
		                },
		                bigger: {
		                    inclusive: "turi turėti ne mažiau kaip",
		                    notInclusive: "turi turėti daugiau kaip",
		                },
		            },
		        },
		    };
		    function getSizing(origin, unitType, inclusive, targetShouldBe) {
		        const result = Sizable[origin] ?? null;
		        if (result === null)
		            return result;
		        return {
		            unit: result.unit[unitType],
		            verb: result.verb[targetShouldBe][inclusive ? "inclusive" : "notInclusive"],
		        };
		    }
		    const FormatDictionary = {
		        regex: "įvestis",
		        email: "el. pašto adresas",
		        url: "URL",
		        emoji: "jaustukas",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO data ir laikas",
		        date: "ISO data",
		        time: "ISO laikas",
		        duration: "ISO trukmė",
		        ipv4: "IPv4 adresas",
		        ipv6: "IPv6 adresas",
		        mac: "MAC adresas",
		        cidrv4: "IPv4 tinklo prefiksas (CIDR)",
		        cidrv6: "IPv6 tinklo prefiksas (CIDR)",
		        base64: "base64 užkoduota eilutė",
		        base64url: "base64url užkoduota eilutė",
		        json_string: "JSON eilutė",
		        e164: "E.164 numeris",
		        credit_card: "kredito kortelės numeris",
		        currency_code: "valiutos kodas",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "įvestis",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "skaičius",
		        bigint: "sveikasis skaičius",
		        string: "eilutė",
		        boolean: "loginė reikšmė",
		        undefined: "neapibrėžta reikšmė",
		        function: "funkcija",
		        symbol: "simbolis",
		        array: "masyvas",
		        object: "objektas",
		        null: "nulinė reikšmė",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Gautas tipas ${received}, o tikėtasi - instanceof ${issue.expected}`;
		                }
		                return `Gautas tipas ${received}, o tikėtasi - ${expected}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Privalo būti ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Privalo būti vienas iš ${util.joinValues(issue.values, "|")} pasirinkimų`;
		            case "too_big": {
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                const sizing = getSizing(issue.origin, getUnitTypeFromNumber(Number(issue.maximum)), issue.inclusive ?? false, "smaller");
		                if (sizing?.verb)
		                    return `${capitalizeFirstCharacter(origin ?? issue.origin ?? "reikšmė")} ${sizing.verb} ${issue.maximum.toString()} ${sizing.unit ?? "elementų"}`;
		                const adj = issue.inclusive ? "ne didesnis kaip" : "mažesnis kaip";
		                return `${capitalizeFirstCharacter(origin ?? issue.origin ?? "reikšmė")} turi būti ${adj} ${issue.maximum.toString()} ${sizing?.unit}`;
		            }
		            case "too_small": {
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                const sizing = getSizing(issue.origin, getUnitTypeFromNumber(Number(issue.minimum)), issue.inclusive ?? false, "bigger");
		                if (sizing?.verb)
		                    return `${capitalizeFirstCharacter(origin ?? issue.origin ?? "reikšmė")} ${sizing.verb} ${issue.minimum.toString()} ${sizing.unit ?? "elementų"}`;
		                const adj = issue.inclusive ? "ne mažesnis kaip" : "didesnis kaip";
		                return `${capitalizeFirstCharacter(origin ?? issue.origin ?? "reikšmė")} turi būti ${adj} ${issue.minimum.toString()} ${sizing?.unit}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Eilutė privalo prasidėti "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Eilutė privalo pasibaigti "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Eilutė privalo įtraukti "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Eilutė privalo atitikti ${_issue.pattern}`;
		                return `Neteisingas ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Skaičius privalo būti ${issue.divisor} kartotinis.`;
		            case "unrecognized_keys":
		                return `Neatpažint${issue.keys.length > 1 ? "i" : "as"} rakt${issue.keys.length > 1 ? "ai" : "as"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return "Rastas klaidingas raktas";
		            case "invalid_union":
		                return "Klaidinga įvestis";
		            case "invalid_element": {
		                const origin = TypeDictionary[issue.origin] ?? issue.origin;
		                return `${capitalizeFirstCharacter(origin ?? issue.origin ?? "reikšmė")} turi klaidingą įvestį`;
		            }
		            default:
		                return "Klaidinga įvestis";
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (lt$1, lt$1.exports));
	return lt$1.exports;
}

var mk$1 = {exports: {}};

var mk = mk$1.exports;

var hasRequiredMk;

function requireMk () {
	if (hasRequiredMk) return mk$1.exports;
	hasRequiredMk = 1;
	(function (module, exports) {
		var __createBinding = (mk && mk.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (mk && mk.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (mk && mk.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "знаци", verb: "да имаат" },
		        file: { unit: "бајти", verb: "да имаат" },
		        array: { unit: "ставки", verb: "да имаат" },
		        set: { unit: "ставки", verb: "да имаат" },
		        map: { unit: "ставки", verb: "да имаат" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "внес",
		        email: "адреса на е-пошта",
		        url: "URL",
		        emoji: "емоџи",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO датум и време",
		        date: "ISO датум",
		        time: "ISO време",
		        duration: "ISO времетраење",
		        ipv4: "IPv4 адреса",
		        ipv6: "IPv6 адреса",
		        mac: "MAC адреса",
		        cidrv4: "IPv4 опсег",
		        cidrv6: "IPv6 опсег",
		        base64: "base64-енкодирана низа",
		        base64url: "base64url-енкодирана низа",
		        json_string: "JSON низа",
		        e164: "E.164 број",
		        credit_card: "број на кредитна картичка",
		        currency_code: "код на валута",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "внес",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "број",
		        array: "низа",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Грешен внес: се очекува instanceof ${issue.expected}, примено ${received}`;
		                }
		                return `Грешен внес: се очекува ${expected}, примено ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Invalid input: expected ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Грешана опција: се очекува една ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Премногу голем: се очекува ${issue.origin ?? "вредноста"} да има ${adj}${issue.maximum.toString()} ${sizing.unit ?? "елементи"}`;
		                return `Премногу голем: се очекува ${issue.origin ?? "вредноста"} да биде ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Премногу мал: се очекува ${issue.origin} да има ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Премногу мал: се очекува ${issue.origin} да биде ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Неважечка низа: мора да започнува со "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Неважечка низа: мора да завршува со "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Неважечка низа: мора да вклучува "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Неважечка низа: мора да одгоара на патернот ${_issue.pattern}`;
		                return `Invalid ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Грешен број: мора да биде делив со ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Непрепознаени клучеви" : "Непрепознаен клуч"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Грешен клуч во ${issue.origin}`;
		            case "invalid_union":
		                return "Грешен внес";
		            case "invalid_element":
		                return `Грешна вредност во ${issue.origin}`;
		            default:
		                return `Грешен внес`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (mk$1, mk$1.exports));
	return mk$1.exports;
}

var ms$1 = {exports: {}};

var ms = ms$1.exports;

var hasRequiredMs;

function requireMs () {
	if (hasRequiredMs) return ms$1.exports;
	hasRequiredMs = 1;
	(function (module, exports) {
		var __createBinding = (ms && ms.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ms && ms.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ms && ms.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "aksara", verb: "mempunyai" },
		        file: { unit: "bait", verb: "mempunyai" },
		        array: { unit: "elemen", verb: "mempunyai" },
		        set: { unit: "elemen", verb: "mempunyai" },
		        map: { unit: "elemen", verb: "mempunyai" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "alamat e-mel",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "tarikh masa ISO",
		        date: "tarikh ISO",
		        time: "masa ISO",
		        duration: "tempoh ISO",
		        ipv4: "alamat IPv4",
		        ipv6: "alamat IPv6",
		        mac: "alamat MAC",
		        cidrv4: "julat IPv4",
		        cidrv6: "julat IPv6",
		        base64: "string dikodkan base64",
		        base64url: "string dikodkan base64url",
		        json_string: "string JSON",
		        e164: "nombor E.164",
		        credit_card: "nombor kad kredit",
		        currency_code: "kod mata wang",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "nombor",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Input tidak sah: dijangka instanceof ${issue.expected}, diterima ${received}`;
		                }
		                return `Input tidak sah: dijangka ${expected}, diterima ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Input tidak sah: dijangka ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Pilihan tidak sah: dijangka salah satu daripada ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Terlalu besar: dijangka ${issue.origin ?? "nilai"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elemen"}`;
		                return `Terlalu besar: dijangka ${issue.origin ?? "nilai"} adalah ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Terlalu kecil: dijangka ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Terlalu kecil: dijangka ${issue.origin} adalah ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `String tidak sah: mesti bermula dengan "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `String tidak sah: mesti berakhir dengan "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `String tidak sah: mesti mengandungi "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `String tidak sah: mesti sepadan dengan corak ${_issue.pattern}`;
		                return `${FormatDictionary[_issue.format] ?? issue.format} tidak sah`;
		            }
		            case "not_multiple_of":
		                return `Nombor tidak sah: perlu gandaan ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Kunci tidak dikenali: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Kunci tidak sah dalam ${issue.origin}`;
		            case "invalid_union":
		                return "Input tidak sah";
		            case "invalid_element":
		                return `Nilai tidak sah dalam ${issue.origin}`;
		            default:
		                return `Input tidak sah`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ms$1, ms$1.exports));
	return ms$1.exports;
}

var ne$1 = {exports: {}};

var ne = ne$1.exports;

var hasRequiredNe;

function requireNe () {
	if (hasRequiredNe) return ne$1.exports;
	hasRequiredNe = 1;
	(function (module, exports) {
		var __createBinding = (ne && ne.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ne && ne.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ne && ne.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "अक्षर", verb: "हुनुपर्छ" },
		        file: { unit: "बाइट", verb: "हुनुपर्छ" },
		        array: { unit: "तत्व", verb: "हुनुपर्छ" },
		        set: { unit: "तत्व", verb: "हुनुपर्छ" },
		        map: { unit: "प्रविष्टि", verb: "हुनुपर्छ" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "इनपुट",
		        email: "इमेल ठेगाना",
		        url: "URL",
		        emoji: "इमोजी",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO मिति र समय",
		        date: "ISO मिति",
		        time: "ISO समय",
		        duration: "ISO अवधि",
		        ipv4: "IPv4 ठेगाना",
		        ipv6: "IPv6 ठेगाना",
		        mac: "MAC ठेगाना",
		        cidrv4: "IPv4 दायरा",
		        cidrv6: "IPv6 दायरा",
		        base64: "base64-इन्कोड गरिएको स्ट्रिङ",
		        base64url: "base64url-इन्कोड गरिएको स्ट्रिङ",
		        json_string: "JSON स्ट्रिङ",
		        e164: "E.164 नम्बर",
		        credit_card: "क्रेडिट कार्ड नम्बर",
		        currency_code: "मुद्रा कोड",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "इनपुट",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `अमान्य इनपुट: अपेक्षित ${expected}, प्राप्त ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `अमान्य इनपुट: अपेक्षित ${util.stringifyPrimitive(issue.values[0])}`;
		                return `अमान्य विकल्प: अपेक्षित मानहरू मध्ये एक ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `धेरै ठूलो: ${issue.origin ?? "मान"} मा ${adj}${issue.maximum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `धेरै ठूलो: ${issue.origin ?? "मान"} ${adj}${issue.maximum.toString()} हुनुपर्छ`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `धेरै सानो: ${issue.origin} मा ${adj}${issue.minimum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `धेरै सानो: ${issue.origin} ${adj}${issue.minimum.toString()} हुनुपर्छ`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `अमान्य स्ट्रिङ: "${_issue.prefix}" बाट सुरु हुनुपर्छ`;
		                if (_issue.format === "ends_with")
		                    return `अमान्य स्ट्रिङ: "${_issue.suffix}" मा समाप्त हुनुपर्छ`;
		                if (_issue.format === "includes")
		                    return `अमान्य स्ट्रिङ: "${_issue.includes}" समावेश हुनुपर्छ`;
		                if (_issue.format === "regex")
		                    return `अमान्य स्ट्रिङ: ढाँचा ${_issue.pattern} सँग मेल खानुपर्छ`;
		                return `अमान्य ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `अमान्य संख्या: ${issue.divisor} को गुणज हुनुपर्छ`;
		            case "unrecognized_keys":
		                return `अपरिचित कुञ्जी${issue.keys.length > 1 ? "हरू" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `अमान्य कुञ्जी: ${issue.origin} मा`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `अमान्य डिस्क्रिमिनेटर मान: अपेक्षित ${opts}`;
		                }
		                return "अमान्य इनपुट";
		            case "invalid_element":
		                return `अमान्य मान: ${issue.origin} मा`;
		            default:
		                return `अमान्य इनपुट`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ne$1, ne$1.exports));
	return ne$1.exports;
}

var nl$1 = {exports: {}};

var nl = nl$1.exports;

var hasRequiredNl;

function requireNl () {
	if (hasRequiredNl) return nl$1.exports;
	hasRequiredNl = 1;
	(function (module, exports) {
		var __createBinding = (nl && nl.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (nl && nl.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (nl && nl.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "tekens", verb: "heeft" },
		        file: { unit: "bytes", verb: "heeft" },
		        array: { unit: "elementen", verb: "heeft" },
		        set: { unit: "elementen", verb: "heeft" },
		        map: { unit: "elementen", verb: "heeft" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "invoer",
		        email: "emailadres",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO datum en tijd",
		        date: "ISO datum",
		        time: "ISO tijd",
		        duration: "ISO duur",
		        ipv4: "IPv4-adres",
		        ipv6: "IPv6-adres",
		        mac: "MAC-adres",
		        cidrv4: "IPv4-bereik",
		        cidrv6: "IPv6-bereik",
		        base64: "base64-gecodeerde tekst",
		        base64url: "base64 URL-gecodeerde tekst",
		        json_string: "JSON string",
		        e164: "E.164-nummer",
		        credit_card: "creditcardnummer",
		        currency_code: "valutacode",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "invoer",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "getal",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ongeldige invoer: verwacht instanceof ${issue.expected}, ontving ${received}`;
		                }
		                return `Ongeldige invoer: verwacht ${expected}, ontving ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ongeldige invoer: verwacht ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ongeldige optie: verwacht één van ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                const longName = issue.origin === "date" ? "laat" : issue.origin === "string" ? "lang" : "groot";
		                if (sizing)
		                    return `Te ${longName}: verwacht dat ${issue.origin ?? "waarde"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementen"} ${sizing.verb}`;
		                return `Te ${longName}: verwacht dat ${issue.origin ?? "waarde"} ${adj}${issue.maximum.toString()} is`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                const shortName = issue.origin === "date" ? "vroeg" : issue.origin === "string" ? "kort" : "klein";
		                if (sizing) {
		                    return `Te ${shortName}: verwacht dat ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} ${sizing.verb}`;
		                }
		                return `Te ${shortName}: verwacht dat ${issue.origin} ${adj}${issue.minimum.toString()} is`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Ongeldige tekst: moet met "${_issue.prefix}" beginnen`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Ongeldige tekst: moet op "${_issue.suffix}" eindigen`;
		                if (_issue.format === "includes")
		                    return `Ongeldige tekst: moet "${_issue.includes}" bevatten`;
		                if (_issue.format === "regex")
		                    return `Ongeldige tekst: moet overeenkomen met patroon ${_issue.pattern}`;
		                return `Ongeldig: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Ongeldig getal: moet een veelvoud van ${issue.divisor} zijn`;
		            case "unrecognized_keys":
		                return `Onbekende key${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Ongeldige key in ${issue.origin}`;
		            case "invalid_union":
		                return "Ongeldige invoer";
		            case "invalid_element":
		                return `Ongeldige waarde in ${issue.origin}`;
		            default:
		                return `Ongeldige invoer`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (nl$1, nl$1.exports));
	return nl$1.exports;
}

var nn$1 = {exports: {}};

var nn = nn$1.exports;

var hasRequiredNn;

function requireNn () {
	if (hasRequiredNn) return nn$1.exports;
	hasRequiredNn = 1;
	(function (module, exports) {
		var __createBinding = (nn && nn.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (nn && nn.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (nn && nn.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "teikn", verb: "å ha" },
		        file: { unit: "bytes", verb: "å ha" },
		        array: { unit: "element", verb: "å innehalde" },
		        set: { unit: "element", verb: "å innehalde" },
		        map: { unit: "element", verb: "å innehalde" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "e-postadresse",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO dato- og klokkeslett",
		        date: "ISO-dato",
		        time: "ISO-klokkeslett",
		        duration: "ISO-varigheit",
		        ipv4: "IPv4-adresse",
		        ipv6: "IPv6-adresse",
		        mac: "MAC-adresse",
		        cidrv4: "IPv4-spekter",
		        cidrv6: "IPv6-spekter",
		        base64: "base64-enkoda streng",
		        base64url: "base64url-enkoda streng",
		        json_string: "JSON-streng",
		        e164: "E.164-nummer",
		        credit_card: "kredittkortnummer",
		        currency_code: "valutakode",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "tal",
		        array: "liste",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ugyldig input: forventa instanceof ${issue.expected}, fekk ${received}`;
		                }
		                return `Ugyldig input: forventa ${expected}, fekk ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ugyldig verdi: forventa ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ugyldig val: forventa eitt av ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `For stor(t): forventa ${issue.origin ?? "value"} til å ha ${adj}${issue.maximum.toString()} ${sizing.unit ?? "element"}`;
		                return `For stor(t): forventa ${issue.origin ?? "value"} til å ha ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `For lite(n): forventa ${issue.origin} til å ha ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `For lite(n): forventa ${issue.origin} til å ha ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Ugyldig streng: må starte med "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Ugyldig streng: må slutte med "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Ugyldig streng: må innehalde "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Ugyldig streng: må matche mønsteret ${_issue.pattern}`;
		                return `Ugyldig ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Ugyldig tal: må vere eit multiplum av ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Ukjende nøklar" : "Ukjend nøkkel"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Ugyldig nøkkel i ${issue.origin}`;
		            case "invalid_union":
		                return "Ugyldig input";
		            case "invalid_element":
		                return `Ugyldig verdi i ${issue.origin}`;
		            default:
		                return `Ugyldig input`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (nn$1, nn$1.exports));
	return nn$1.exports;
}

var no$1 = {exports: {}};

var no = no$1.exports;

var hasRequiredNo;

function requireNo () {
	if (hasRequiredNo) return no$1.exports;
	hasRequiredNo = 1;
	(function (module, exports) {
		var __createBinding = (no && no.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (no && no.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (no && no.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "tegn", verb: "å ha" },
		        file: { unit: "bytes", verb: "å ha" },
		        array: { unit: "elementer", verb: "å inneholde" },
		        set: { unit: "elementer", verb: "å inneholde" },
		        map: { unit: "elementer", verb: "å inneholde" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "input",
		        email: "e-postadresse",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO dato- og klokkeslett",
		        date: "ISO-dato",
		        time: "ISO-klokkeslett",
		        duration: "ISO-varighet",
		        ipv4: "IPv4-adresse",
		        ipv6: "IPv6-adresse",
		        mac: "MAC-adresse",
		        cidrv4: "IPv4-spekter",
		        cidrv6: "IPv6-spekter",
		        base64: "base64-enkodet streng",
		        base64url: "base64url-enkodet streng",
		        json_string: "JSON-streng",
		        e164: "E.164-nummer",
		        credit_card: "kredittkortnummer",
		        currency_code: "valutakode",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "tall",
		        array: "liste",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ugyldig input: forventet instanceof ${issue.expected}, fikk ${received}`;
		                }
		                return `Ugyldig input: forventet ${expected}, fikk ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ugyldig verdi: forventet ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ugyldig valg: forventet en av ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `For stor(t): forventet ${issue.origin ?? "value"} til å ha ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementer"}`;
		                return `For stor(t): forventet ${issue.origin ?? "value"} til å ha ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `For lite(n): forventet ${issue.origin} til å ha ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `For lite(n): forventet ${issue.origin} til å ha ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Ugyldig streng: må starte med "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Ugyldig streng: må ende med "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Ugyldig streng: må inneholde "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Ugyldig streng: må matche mønsteret ${_issue.pattern}`;
		                return `Ugyldig ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Ugyldig tall: må være et multiplum av ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Ukjente nøkler" : "Ukjent nøkkel"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Ugyldig nøkkel i ${issue.origin}`;
		            case "invalid_union":
		                return "Ugyldig input";
		            case "invalid_element":
		                return `Ugyldig verdi i ${issue.origin}`;
		            default:
		                return `Ugyldig input`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (no$1, no$1.exports));
	return no$1.exports;
}

var ota$1 = {exports: {}};

var ota = ota$1.exports;

var hasRequiredOta;

function requireOta () {
	if (hasRequiredOta) return ota$1.exports;
	hasRequiredOta = 1;
	(function (module, exports) {
		var __createBinding = (ota && ota.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ota && ota.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ota && ota.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "harf", verb: "olmalıdır" },
		        file: { unit: "bayt", verb: "olmalıdır" },
		        array: { unit: "unsur", verb: "olmalıdır" },
		        set: { unit: "unsur", verb: "olmalıdır" },
		        map: { unit: "unsur", verb: "olmalıdır" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "giren",
		        email: "epostagâh",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO hengâmı",
		        date: "ISO tarihi",
		        time: "ISO zamanı",
		        duration: "ISO müddeti",
		        ipv4: "IPv4 nişânı",
		        ipv6: "IPv6 nişânı",
		        mac: "MAC nişânı",
		        cidrv4: "IPv4 menzili",
		        cidrv6: "IPv6 menzili",
		        base64: "base64-şifreli metin",
		        base64url: "base64url-şifreli metin",
		        json_string: "JSON metin",
		        e164: "E.164 sayısı",
		        credit_card: "i'tibâr kartı numarası",
		        currency_code: "para birimi kodu",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "giren",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "numara",
		        array: "saf",
		        null: "gayb",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Fâsit giren: umulan instanceof ${issue.expected}, alınan ${received}`;
		                }
		                return `Fâsit giren: umulan ${expected}, alınan ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Fâsit giren: umulan ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Fâsit tercih: mûteberler ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Fazla büyük: ${issue.origin ?? "value"}, ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elements"} sahip olmalıydı.`;
		                return `Fazla büyük: ${issue.origin ?? "value"}, ${adj}${issue.maximum.toString()} olmalıydı.`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Fazla küçük: ${issue.origin}, ${adj}${issue.minimum.toString()} ${sizing.unit} sahip olmalıydı.`;
		                }
		                return `Fazla küçük: ${issue.origin}, ${adj}${issue.minimum.toString()} olmalıydı.`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Fâsit metin: "${_issue.prefix}" ile başlamalı.`;
		                if (_issue.format === "ends_with")
		                    return `Fâsit metin: "${_issue.suffix}" ile bitmeli.`;
		                if (_issue.format === "includes")
		                    return `Fâsit metin: "${_issue.includes}" ihtivâ etmeli.`;
		                if (_issue.format === "regex")
		                    return `Fâsit metin: ${_issue.pattern} nakşına uymalı.`;
		                return `Fâsit ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Fâsit sayı: ${issue.divisor} katı olmalıydı.`;
		            case "unrecognized_keys":
		                return `Tanınmayan anahtar ${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} için tanınmayan anahtar var.`;
		            case "invalid_union":
		                return "Giren tanınamadı.";
		            case "invalid_element":
		                return `${issue.origin} için tanınmayan kıymet var.`;
		            default:
		                return `Kıymet tanınamadı.`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ota$1, ota$1.exports));
	return ota$1.exports;
}

var ps$1 = {exports: {}};

var ps = ps$1.exports;

var hasRequiredPs;

function requirePs () {
	if (hasRequiredPs) return ps$1.exports;
	hasRequiredPs = 1;
	(function (module, exports) {
		var __createBinding = (ps && ps.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ps && ps.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ps && ps.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "توکي", verb: "ولري" },
		        file: { unit: "بایټس", verb: "ولري" },
		        array: { unit: "توکي", verb: "ولري" },
		        set: { unit: "توکي", verb: "ولري" },
		        map: { unit: "توکي", verb: "ولري" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ورودي",
		        email: "بریښنالیک",
		        url: "یو آر ال",
		        emoji: "ایموجي",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "نیټه او وخت",
		        date: "نېټه",
		        time: "وخت",
		        duration: "موده",
		        ipv4: "د IPv4 پته",
		        ipv6: "د IPv6 پته",
		        mac: "د MAC پته",
		        cidrv4: "د IPv4 ساحه",
		        cidrv6: "د IPv6 ساحه",
		        base64: "base64-encoded متن",
		        base64url: "base64url-encoded متن",
		        json_string: "JSON متن",
		        e164: "د E.164 شمېره",
		        credit_card: "د کریډیټ کارت شمیره",
		        currency_code: "د اسعارو کوډ",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ورودي",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "عدد",
		        array: "ارې",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `ناسم ورودي: باید instanceof ${issue.expected} وای, مګر ${received} ترلاسه شو`;
		                }
		                return `ناسم ورودي: باید ${expected} وای, مګر ${received} ترلاسه شو`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1) {
		                    return `ناسم ورودي: باید ${util.stringifyPrimitive(issue.values[0])} وای`;
		                }
		                return `ناسم انتخاب: باید یو له ${util.joinValues(issue.values, "|")} څخه وای`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `ډیر لوی: ${issue.origin ?? "ارزښت"} باید ${adj}${issue.maximum.toString()} ${sizing.unit ?? "عنصرونه"} ولري`;
		                }
		                return `ډیر لوی: ${issue.origin ?? "ارزښت"} باید ${adj}${issue.maximum.toString()} وي`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `ډیر کوچنی: ${issue.origin} باید ${adj}${issue.minimum.toString()} ${sizing.unit} ولري`;
		                }
		                return `ډیر کوچنی: ${issue.origin} باید ${adj}${issue.minimum.toString()} وي`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `ناسم متن: باید د "${_issue.prefix}" سره پیل شي`;
		                }
		                if (_issue.format === "ends_with") {
		                    return `ناسم متن: باید د "${_issue.suffix}" سره پای ته ورسيږي`;
		                }
		                if (_issue.format === "includes") {
		                    return `ناسم متن: باید "${_issue.includes}" ولري`;
		                }
		                if (_issue.format === "regex") {
		                    return `ناسم متن: باید د ${_issue.pattern} سره مطابقت ولري`;
		                }
		                return `${FormatDictionary[_issue.format] ?? issue.format} ناسم دی`;
		            }
		            case "not_multiple_of":
		                return `ناسم عدد: باید د ${issue.divisor} مضرب وي`;
		            case "unrecognized_keys":
		                return `ناسم ${issue.keys.length > 1 ? "کلیډونه" : "کلیډ"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `ناسم کلیډ په ${issue.origin} کې`;
		            case "invalid_union":
		                return `ناسمه ورودي`;
		            case "invalid_element":
		                return `ناسم عنصر په ${issue.origin} کې`;
		            default:
		                return `ناسمه ورودي`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ps$1, ps$1.exports));
	return ps$1.exports;
}

var pl$1 = {exports: {}};

var pl = pl$1.exports;

var hasRequiredPl;

function requirePl () {
	if (hasRequiredPl) return pl$1.exports;
	hasRequiredPl = 1;
	(function (module, exports) {
		var __createBinding = (pl && pl.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (pl && pl.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (pl && pl.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "znaków", verb: "mieć" },
		        file: { unit: "bajtów", verb: "mieć" },
		        array: { unit: "elementów", verb: "mieć" },
		        set: { unit: "elementów", verb: "mieć" },
		        map: { unit: "elementów", verb: "mieć" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "wyrażenie",
		        email: "adres email",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "data i godzina w formacie ISO",
		        date: "data w formacie ISO",
		        time: "godzina w formacie ISO",
		        duration: "czas trwania ISO",
		        ipv4: "adres IPv4",
		        ipv6: "adres IPv6",
		        mac: "adres MAC",
		        cidrv4: "zakres IPv4",
		        cidrv6: "zakres IPv6",
		        base64: "ciąg znaków zakodowany w formacie base64",
		        base64url: "ciąg znaków zakodowany w formacie base64url",
		        json_string: "ciąg znaków w formacie JSON",
		        e164: "liczba E.164",
		        credit_card: "numer karty kredytowej",
		        currency_code: "kod waluty",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "wejście",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "liczba",
		        array: "tablica",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Nieprawidłowe dane wejściowe: oczekiwano instanceof ${issue.expected}, otrzymano ${received}`;
		                }
		                return `Nieprawidłowe dane wejściowe: oczekiwano ${expected}, otrzymano ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Nieprawidłowe dane wejściowe: oczekiwano ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Nieprawidłowa opcja: oczekiwano jednej z wartości ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Za duża wartość: oczekiwano, że ${issue.origin ?? "wartość"} będzie mieć ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementów"}`;
		                }
		                return `Zbyt duż(y/a/e): oczekiwano, że ${issue.origin ?? "wartość"} będzie wynosić ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Za mała wartość: oczekiwano, że ${issue.origin ?? "wartość"} będzie mieć ${adj}${issue.minimum.toString()} ${sizing.unit ?? "elementów"}`;
		                }
		                return `Zbyt mał(y/a/e): oczekiwano, że ${issue.origin ?? "wartość"} będzie wynosić ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Nieprawidłowy ciąg znaków: musi zaczynać się od "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Nieprawidłowy ciąg znaków: musi kończyć się na "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Nieprawidłowy ciąg znaków: musi zawierać "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Nieprawidłowy ciąg znaków: musi odpowiadać wzorcowi ${_issue.pattern}`;
		                return `Nieprawidłow(y/a/e) ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Nieprawidłowa liczba: musi być wielokrotnością ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Nierozpoznane klucze${issue.keys.length > 1 ? "s" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Nieprawidłowy klucz w ${issue.origin}`;
		            case "invalid_union":
		                return "Nieprawidłowe dane wejściowe";
		            case "invalid_element":
		                return `Nieprawidłowa wartość w ${issue.origin}`;
		            default:
		                return `Nieprawidłowe dane wejściowe`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (pl$1, pl$1.exports));
	return pl$1.exports;
}

var pt$1 = {exports: {}};

var pt = pt$1.exports;

var hasRequiredPt;

function requirePt () {
	if (hasRequiredPt) return pt$1.exports;
	hasRequiredPt = 1;
	(function (module, exports) {
		var __createBinding = (pt && pt.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (pt && pt.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (pt && pt.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caracteres" },
		        file: { unit: "bytes" },
		        array: { unit: "elementos" },
		        set: { unit: "elementos" },
		        map: { unit: "entradas" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "a entrada",
		        email: "o endereço de e-mail",
		        url: "o URL",
		        emoji: "o emoji",
		        uuid: "o UUID",
		        uuidv4: "o UUIDv4",
		        uuidv6: "o UUIDv6",
		        nanoid: "o nanoid",
		        guid: "o GUID",
		        cuid: "o cuid",
		        cuid2: "o cuid2",
		        ulid: "o ULID",
		        xid: "o XID",
		        ksuid: "o KSUID",
		        datetime: "a data e hora ISO",
		        date: "a data ISO",
		        time: "a hora ISO",
		        duration: "a duração ISO",
		        ipv4: "o endereço IPv4",
		        ipv6: "o endereço IPv6",
		        mac: "o endereço MAC",
		        cidrv4: "o intervalo de endereços IPv4",
		        cidrv6: "o intervalo de endereços IPv6",
		        base64: "o texto codificado em base64",
		        base64url: "o texto codificado em base64url",
		        json_string: "o texto JSON",
		        e164: "o número E.164",
		        credit_card: "o número de cartão de crédito",
		        currency_code: "o código de moeda",
		        iban: "o IBAN",
		        jwt: "o JWT",
		        template_literal: "a entrada",
		    };
		    const Gender = {
		        masculine: { definite: "o", indefinite: "um" },
		        feminine: { definite: "a", indefinite: "uma" },
		    };
		    const TypeDictionary = {
		        string: { name: "texto", articles: Gender.masculine },
		        number: { name: "número", articles: Gender.masculine },
		        int: { name: "número inteiro", articles: Gender.masculine },
		        boolean: { name: "valor booleano", articles: Gender.masculine },
		        bigint: { name: "número bigint", articles: Gender.masculine },
		        symbol: { name: "símbolo", articles: Gender.masculine },
		        undefined: { name: 'valor "undefined"', articles: Gender.masculine },
		        null: { name: 'valor "nulo"', articles: Gender.masculine },
		        never: { name: 'valor "never"', articles: Gender.masculine },
		        void: { name: 'valor "void"', articles: Gender.masculine },
		        date: { name: "data", articles: Gender.feminine },
		        array: { name: "vetor", articles: Gender.masculine },
		        object: { name: "objeto", articles: Gender.masculine },
		        tuple: { name: "tuplo", articles: Gender.masculine },
		        record: { name: "registo", articles: Gender.masculine },
		        map: { name: "mapa", articles: Gender.masculine },
		        set: { name: "conjunto", articles: Gender.masculine },
		        file: { name: "ficheiro", articles: Gender.masculine },
		        nonoptional: { name: "valor não opcional", articles: Gender.masculine },
		        nan: { name: 'valor "NaN"', articles: Gender.masculine }, // Compatibility: "nan" -> "NaN" for display
		        function: { name: "função", articles: Gender.feminine },
		    };
		    function translateOriginWithArticle(type, articleType) {
		        const translatedValue = TypeDictionary[type] ?? { name: `valor "${type}"`, articles: Gender.masculine };
		        return `${translatedValue.articles[articleType]} ${translatedValue.name}`;
		    }
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = translateOriginWithArticle(issue.expected, "indefinite");
		                const receivedType = util.parsedType(issue.input);
		                const received = translateOriginWithArticle(receivedType, "indefinite");
		                return `Entrada inválida: esperava ${expected}, recebeu ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Entrada inválida: esperava ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Opção inválida: esperava uma das seguintes opções: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Demasiado grande: esperava que ${translateOriginWithArticle(issue.origin, "definite")} tivesse ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "elementos"}`;
		                return `Demasiado grande: esperava que ${translateOriginWithArticle(issue.origin, "definite")} fosse ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Demasiado pequeno: esperava que ${translateOriginWithArticle(issue.origin, "definite")} tivesse ${adj} ${issue.minimum.toString()} ${sizing.unit ?? "elementos"}`;
		                }
		                return `Demasiado pequeno: esperava que ${translateOriginWithArticle(issue.origin, "definite")} fosse ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Texto inválido: deve começar por "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Texto inválido: deve terminar em "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Texto inválido: deve incluir "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Texto inválido: deve corresponder ao padrão ${_issue.pattern}`;
		                return `Formato d${FormatDictionary[_issue.format] ?? issue.format} inválido`;
		            }
		            case "not_multiple_of":
		                return `Número inválido: deve ser múltiplo de ${issue.divisor}`;
		            case "unrecognized_keys": {
		                const plural = issue.keys.length > 1 ? "s" : "";
		                return `Chave${plural} inválida${plural}: ${util.joinValues(issue.keys, ", ")}`;
		            }
		            case "invalid_key":
		                return `Entrada inválida n${translateOriginWithArticle(issue.origin, "definite")}`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `Valor de discriminação inválido. Esperava ${opts}`;
		                }
		                return "Entrada inválida";
		            case "invalid_element":
		                return `Entrada inválida n${translateOriginWithArticle(issue.origin, "definite")}`;
		            default:
		                return `Entrada inválida`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (pt$1, pt$1.exports));
	return pt$1.exports;
}

var ptBR$1 = {exports: {}};

var ptBR = ptBR$1.exports;

var hasRequiredPtBR;

function requirePtBR () {
	if (hasRequiredPtBR) return ptBR$1.exports;
	hasRequiredPtBR = 1;
	(function (module, exports) {
		var __createBinding = (ptBR && ptBR.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ptBR && ptBR.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ptBR && ptBR.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caracteres" },
		        file: { unit: "bytes" },
		        array: { unit: "elementos" },
		        set: { unit: "elementos" },
		        map: { unit: "entradas" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "a entrada",
		        email: "o endereço de e-mail",
		        url: "o URL",
		        emoji: "o emoji",
		        uuid: "o UUID",
		        uuidv4: "o UUIDv4",
		        uuidv6: "o UUIDv6",
		        nanoid: "o nanoid",
		        guid: "o GUID",
		        cuid: "o cuid",
		        cuid2: "o cuid2",
		        ulid: "o ULID",
		        xid: "o XID",
		        ksuid: "o KSUID",
		        datetime: "a data e hora ISO",
		        date: "a data ISO",
		        time: "a hora ISO",
		        duration: "a duração ISO",
		        ipv4: "o endereço IPv4",
		        ipv6: "o endereço IPv6",
		        mac: "o endereço MAC",
		        cidrv4: "a faixa de endereços IPv4",
		        cidrv6: "a faixa de endereços IPv6",
		        base64: "o texto codificado em base64",
		        base64url: "o texto codificado em base64url",
		        json_string: "o texto JSON",
		        e164: "o número E.164",
		        credit_card: "o número de cartão de crédito",
		        currency_code: "o código de moeda",
		        iban: "o IBAN",
		        jwt: "o JWT",
		        template_literal: "a entrada",
		    };
		    const Gender = {
		        masculine: { definite: "o", indefinite: "um" },
		        feminine: { definite: "a", indefinite: "uma" },
		    };
		    const TypeDictionary = {
		        string: { name: "texto", articles: Gender.masculine },
		        number: { name: "número", articles: Gender.masculine },
		        int: { name: "número inteiro", articles: Gender.masculine },
		        boolean: { name: "valor booleano", articles: Gender.masculine },
		        bigint: { name: "número bigint", articles: Gender.masculine },
		        symbol: { name: "símbolo", articles: Gender.masculine },
		        undefined: { name: 'valor "undefined"', articles: Gender.masculine },
		        null: { name: 'valor "nulo"', articles: Gender.masculine },
		        never: { name: 'valor "never"', articles: Gender.masculine },
		        void: { name: 'valor "void"', articles: Gender.masculine },
		        date: { name: "data", articles: Gender.feminine },
		        array: { name: "vetor", articles: Gender.masculine },
		        object: { name: "objeto", articles: Gender.masculine },
		        tuple: { name: "tupla", articles: Gender.feminine },
		        record: { name: "registro", articles: Gender.masculine },
		        map: { name: "mapa", articles: Gender.masculine },
		        set: { name: "conjunto", articles: Gender.masculine },
		        file: { name: "arquivo", articles: Gender.masculine },
		        nonoptional: { name: "valor não opcional", articles: Gender.masculine },
		        nan: { name: 'valor "NaN"', articles: Gender.masculine }, // Compatibility: "nan" -> "NaN" for display
		        function: { name: "função", articles: Gender.feminine },
		    };
		    function translateOriginWithArticle(type, articleType) {
		        const translatedValue = TypeDictionary[type] ?? { name: `valor "${type}"`, articles: Gender.masculine };
		        return `${translatedValue.articles[articleType]} ${translatedValue.name}`;
		    }
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = translateOriginWithArticle(issue.expected, "indefinite");
		                const receivedType = util.parsedType(issue.input);
		                const received = translateOriginWithArticle(receivedType, "indefinite");
		                return `Entrada inválida: esperava ${expected}, recebeu ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Entrada inválida: esperava ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Opção inválida: esperava uma das seguintes opções: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Grande demais: esperava que ${translateOriginWithArticle(issue.origin, "definite")} tivesse ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "elementos"}`;
		                return `Grande demais: esperava que ${translateOriginWithArticle(issue.origin, "definite")} fosse ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Pequeno demais: esperava que ${translateOriginWithArticle(issue.origin, "definite")} tivesse ${adj} ${issue.minimum.toString()} ${sizing.unit ?? "elementos"}`;
		                }
		                return `Pequeno demais: esperava que ${translateOriginWithArticle(issue.origin, "definite")} fosse ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Texto inválido: deve começar com "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Texto inválido: deve terminar com "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Texto inválido: deve incluir "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Texto inválido: deve corresponder ao padrão ${_issue.pattern}`;
		                return `Formato d${FormatDictionary[_issue.format] ?? issue.format} inválido`;
		            }
		            case "not_multiple_of":
		                return `Número inválido: deve ser múltiplo de ${issue.divisor}`;
		            case "unrecognized_keys": {
		                const plural = issue.keys.length > 1 ? "s" : "";
		                return `Chave${plural} inválida${plural}: ${util.joinValues(issue.keys, ", ")}`;
		            }
		            case "invalid_key":
		                return `Entrada inválida n${translateOriginWithArticle(issue.origin, "definite")}`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `Valor de discriminação inválido. Esperava ${opts}`;
		                }
		                return "Entrada inválida";
		            case "invalid_element":
		                return `Entrada inválida n${translateOriginWithArticle(issue.origin, "definite")}`;
		            default:
		                return `Entrada inválida`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ptBR$1, ptBR$1.exports));
	return ptBR$1.exports;
}

var ro$1 = {exports: {}};

var ro = ro$1.exports;

var hasRequiredRo;

function requireRo () {
	if (hasRequiredRo) return ro$1.exports;
	hasRequiredRo = 1;
	(function (module, exports) {
		var __createBinding = (ro && ro.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ro && ro.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ro && ro.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "caractere", verb: "să aibă" },
		        file: { unit: "octeți", verb: "să aibă" },
		        array: { unit: "elemente", verb: "să aibă" },
		        set: { unit: "elemente", verb: "să aibă" },
		        map: { unit: "intrări", verb: "să aibă" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "intrare",
		        email: "adresă de email",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "dată și oră ISO",
		        date: "dată ISO",
		        time: "oră ISO",
		        duration: "durată ISO",
		        ipv4: "adresă IPv4",
		        ipv6: "adresă IPv6",
		        mac: "adresă MAC",
		        cidrv4: "interval IPv4",
		        cidrv6: "interval IPv6",
		        base64: "șir codat base64",
		        base64url: "șir codat base64url",
		        json_string: "șir JSON",
		        e164: "număr E.164",
		        credit_card: "număr de card de credit",
		        currency_code: "cod valutar",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "intrare",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        string: "șir",
		        number: "număr",
		        boolean: "boolean",
		        function: "funcție",
		        array: "matrice",
		        object: "obiect",
		        undefined: "nedefinit",
		        symbol: "simbol",
		        bigint: "număr mare",
		        void: "void",
		        never: "never",
		        map: "hartă",
		        set: "set",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `Intrare invalidă: așteptat ${expected}, primit ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Intrare invalidă: așteptat ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Opțiune invalidă: așteptat una dintre ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Prea mare: așteptat ca ${issue.origin ?? "valoarea"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elemente"}`;
		                return `Prea mare: așteptat ca ${issue.origin ?? "valoarea"} să fie ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Prea mic: așteptat ca ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Prea mic: așteptat ca ${issue.origin} să fie ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Șir invalid: trebuie să înceapă cu "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Șir invalid: trebuie să se termine cu "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Șir invalid: trebuie să includă "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Șir invalid: trebuie să se potrivească cu modelul ${_issue.pattern}`;
		                return `Format invalid: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Număr invalid: trebuie să fie multiplu de ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Chei nerecunoscute: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Cheie invalidă în ${issue.origin}`;
		            case "invalid_union":
		                return "Intrare invalidă";
		            case "invalid_element":
		                return `Valoare invalidă în ${issue.origin}`;
		            default:
		                return `Intrare invalidă`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ro$1, ro$1.exports));
	return ro$1.exports;
}

var ru$1 = {exports: {}};

var ru = ru$1.exports;

var hasRequiredRu;

function requireRu () {
	if (hasRequiredRu) return ru$1.exports;
	hasRequiredRu = 1;
	(function (module, exports) {
		var __createBinding = (ru && ru.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ru && ru.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ru && ru.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		function getRussianPlural(count, one, few, many) {
		    const absCount = Math.abs(count);
		    const lastDigit = absCount % 10;
		    const lastTwoDigits = absCount % 100;
		    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
		        return many;
		    }
		    if (lastDigit === 1) {
		        return one;
		    }
		    if (lastDigit >= 2 && lastDigit <= 4) {
		        return few;
		    }
		    return many;
		}
		const error = () => {
		    const Sizable = {
		        string: {
		            unit: {
		                one: "символ",
		                few: "символа",
		                many: "символов",
		            },
		            verb: "иметь",
		        },
		        file: {
		            unit: {
		                one: "байт",
		                few: "байта",
		                many: "байт",
		            },
		            verb: "иметь",
		        },
		        array: {
		            unit: {
		                one: "элемент",
		                few: "элемента",
		                many: "элементов",
		            },
		            verb: "иметь",
		        },
		        set: {
		            unit: {
		                one: "элемент",
		                few: "элемента",
		                many: "элементов",
		            },
		            verb: "иметь",
		        },
		        map: {
		            unit: {
		                one: "элемент",
		                few: "элемента",
		                many: "элементов",
		            },
		            verb: "иметь",
		        },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ввод",
		        email: "email адрес",
		        url: "URL",
		        emoji: "эмодзи",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO дата и время",
		        date: "ISO дата",
		        time: "ISO время",
		        duration: "ISO длительность",
		        ipv4: "IPv4 адрес",
		        ipv6: "IPv6 адрес",
		        mac: "MAC адрес",
		        cidrv4: "IPv4 диапазон",
		        cidrv6: "IPv6 диапазон",
		        base64: "строка в формате base64",
		        base64url: "строка в формате base64url",
		        json_string: "JSON строка",
		        e164: "номер E.164",
		        credit_card: "номер кредитной карты",
		        currency_code: "код валюты",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ввод",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "число",
		        array: "массив",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Неверный ввод: ожидалось instanceof ${issue.expected}, получено ${received}`;
		                }
		                return `Неверный ввод: ожидалось ${expected}, получено ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Неверный ввод: ожидалось ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Неверный вариант: ожидалось одно из ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    const maxValue = Number(issue.maximum);
		                    const unit = getRussianPlural(maxValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
		                    return `Слишком большое значение: ожидалось, что ${issue.origin ?? "значение"} будет иметь ${adj}${issue.maximum.toString()} ${unit}`;
		                }
		                return `Слишком большое значение: ожидалось, что ${issue.origin ?? "значение"} будет ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    const minValue = Number(issue.minimum);
		                    const unit = getRussianPlural(minValue, sizing.unit.one, sizing.unit.few, sizing.unit.many);
		                    return `Слишком маленькое значение: ожидалось, что ${issue.origin} будет иметь ${adj}${issue.minimum.toString()} ${unit}`;
		                }
		                return `Слишком маленькое значение: ожидалось, что ${issue.origin} будет ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Неверная строка: должна начинаться с "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Неверная строка: должна заканчиваться на "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Неверная строка: должна содержать "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Неверная строка: должна соответствовать шаблону ${_issue.pattern}`;
		                return `Неверный ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Неверное число: должно быть кратным ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Нераспознанн${issue.keys.length > 1 ? "ые" : "ый"} ключ${issue.keys.length > 1 ? "и" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Неверный ключ в ${issue.origin}`;
		            case "invalid_union":
		                return "Неверные входные данные";
		            case "invalid_element":
		                return `Неверное значение в ${issue.origin}`;
		            default:
		                return `Неверные входные данные`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ru$1, ru$1.exports));
	return ru$1.exports;
}

var sk$1 = {exports: {}};

var sk = sk$1.exports;

var hasRequiredSk;

function requireSk () {
	if (hasRequiredSk) return sk$1.exports;
	hasRequiredSk = 1;
	(function (module, exports) {
		var __createBinding = (sk && sk.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (sk && sk.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (sk && sk.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "znakov", verb: "mať" },
		        file: { unit: "bajtov", verb: "mať" },
		        array: { unit: "prvkov", verb: "mať" },
		        set: { unit: "prvkov", verb: "mať" },
		        map: { unit: "položiek", verb: "mať" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "regulárny výraz",
		        email: "e-mailová adresa",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "dátum a čas vo formáte ISO",
		        date: "dátum vo formáte ISO",
		        time: "čas vo formáte ISO",
		        duration: "doba trvania ISO",
		        ipv4: "IPv4 adresa",
		        ipv6: "IPv6 adresa",
		        mac: "MAC adresa",
		        cidrv4: "rozsah IPv4",
		        cidrv6: "rozsah IPv6",
		        base64: "reťazec zakódovaný vo formáte base64",
		        base64url: "reťazec zakódovaný vo formáte base64url",
		        json_string: "reťazec vo formáte JSON",
		        e164: "číslo E.164",
		        credit_card: "číslo kreditnej karty",
		        currency_code: "kód meny",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "vstup",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "číslo",
		        string: "reťazec",
		        function: "funkcia",
		        array: "pole",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Neplatný vstup: očakávané instanceof ${issue.expected}, obdržané ${received}`;
		                }
		                return `Neplatný vstup: očakávané ${expected}, obdržané ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Neplatný vstup: očakávané ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Neplatný vstup: očakávaná jedna z hodnôt ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Hodnota je príliš veľká: ${issue.origin ?? "hodnota"} musí mať ${adj}${issue.maximum.toString()} ${sizing.unit ?? "prvkov"}`;
		                }
		                return `Hodnota je príliš veľká: ${issue.origin ?? "hodnota"} musí byť ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Hodnota je príliš malá: ${issue.origin ?? "hodnota"} musí mať ${adj}${issue.minimum.toString()} ${sizing.unit ?? "prvkov"}`;
		                }
		                return `Hodnota je príliš malá: ${issue.origin ?? "hodnota"} musí byť ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Neplatný reťazec: musí začínať na "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Neplatný reťazec: musí končiť na "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Neplatný reťazec: musí obsahovať "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Neplatný reťazec: musí zodpovedať vzoru ${_issue.pattern}`;
		                return `Neplatný formát ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Neplatné číslo: musí byť násobkom ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Neznáme klúče: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Neplatný klúč v ${issue.origin}`;
		            case "invalid_union":
		                return "Neplatný vstup";
		            case "invalid_element":
		                return `Neplatná hodnota v ${issue.origin}`;
		            default:
		                return `Neplatný vstup`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (sk$1, sk$1.exports));
	return sk$1.exports;
}

var sl$1 = {exports: {}};

var sl = sl$1.exports;

var hasRequiredSl;

function requireSl () {
	if (hasRequiredSl) return sl$1.exports;
	hasRequiredSl = 1;
	(function (module, exports) {
		var __createBinding = (sl && sl.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (sl && sl.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (sl && sl.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "znakov", verb: "imeti" },
		        file: { unit: "bajtov", verb: "imeti" },
		        array: { unit: "elementov", verb: "imeti" },
		        set: { unit: "elementov", verb: "imeti" },
		        map: { unit: "elementov", verb: "imeti" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "vnos",
		        email: "e-poštni naslov",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO datum in čas",
		        date: "ISO datum",
		        time: "ISO čas",
		        duration: "ISO trajanje",
		        ipv4: "IPv4 naslov",
		        ipv6: "IPv6 naslov",
		        mac: "MAC naslov",
		        cidrv4: "obseg IPv4",
		        cidrv6: "obseg IPv6",
		        base64: "base64 kodiran niz",
		        base64url: "base64url kodiran niz",
		        json_string: "JSON niz",
		        e164: "E.164 številka",
		        credit_card: "številka kreditne kartice",
		        currency_code: "koda valute",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "vnos",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "število",
		        array: "tabela",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Neveljaven vnos: pričakovano instanceof ${issue.expected}, prejeto ${received}`;
		                }
		                return `Neveljaven vnos: pričakovano ${expected}, prejeto ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Neveljaven vnos: pričakovano ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Neveljavna možnost: pričakovano eno izmed ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Preveliko: pričakovano, da bo ${issue.origin ?? "vrednost"} imelo ${adj}${issue.maximum.toString()} ${sizing.unit ?? "elementov"}`;
		                return `Preveliko: pričakovano, da bo ${issue.origin ?? "vrednost"} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Premajhno: pričakovano, da bo ${issue.origin} imelo ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Premajhno: pričakovano, da bo ${issue.origin} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Neveljaven niz: mora se začeti z "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Neveljaven niz: mora se končati z "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Neveljaven niz: mora vsebovati "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Neveljaven niz: mora ustrezati vzorcu ${_issue.pattern}`;
		                return `Neveljaven ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Neveljavno število: mora biti večkratnik ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Neprepoznan${issue.keys.length > 1 ? "i ključi" : " ključ"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Neveljaven ključ v ${issue.origin}`;
		            case "invalid_union":
		                return "Neveljaven vnos";
		            case "invalid_element":
		                return `Neveljavna vrednost v ${issue.origin}`;
		            default:
		                return "Neveljaven vnos";
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (sl$1, sl$1.exports));
	return sl$1.exports;
}

var sv$1 = {exports: {}};

var sv = sv$1.exports;

var hasRequiredSv;

function requireSv () {
	if (hasRequiredSv) return sv$1.exports;
	hasRequiredSv = 1;
	(function (module, exports) {
		var __createBinding = (sv && sv.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (sv && sv.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (sv && sv.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "tecken", verb: "att ha" },
		        file: { unit: "bytes", verb: "att ha" },
		        array: { unit: "objekt", verb: "att innehålla" },
		        set: { unit: "objekt", verb: "att innehålla" },
		        map: { unit: "objekt", verb: "att innehålla" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "reguljärt uttryck",
		        email: "e-postadress",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO-datum och tid",
		        date: "ISO-datum",
		        time: "ISO-tid",
		        duration: "ISO-varaktighet",
		        ipv4: "IPv4-adress",
		        ipv6: "IPv6-adress",
		        mac: "MAC-adress",
		        cidrv4: "IPv4-spektrum",
		        cidrv6: "IPv6-spektrum",
		        base64: "base64-kodad sträng",
		        base64url: "base64url-kodad sträng",
		        json_string: "JSON-sträng",
		        e164: "E.164-nummer",
		        credit_card: "kreditkortsnummer",
		        currency_code: "valutakod",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "mall-literal",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "antal",
		        array: "lista",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ogiltig inmatning: förväntat instanceof ${issue.expected}, fick ${received}`;
		                }
		                return `Ogiltig inmatning: förväntat ${expected}, fick ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ogiltig inmatning: förväntat ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Ogiltigt val: förväntade en av ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `För stor(t): förväntade ${issue.origin ?? "värdet"} att ha ${adj}${issue.maximum.toString()} ${sizing.unit ?? "element"}`;
		                }
		                return `För stor(t): förväntat ${issue.origin ?? "värdet"} att ha ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `För lite(t): förväntade ${issue.origin ?? "värdet"} att ha ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `För lite(t): förväntade ${issue.origin ?? "värdet"} att ha ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `Ogiltig sträng: måste börja med "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `Ogiltig sträng: måste sluta med "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Ogiltig sträng: måste innehålla "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Ogiltig sträng: måste matcha mönstret "${_issue.pattern}"`;
		                return `Ogiltig(t) ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Ogiltigt tal: måste vara en multipel av ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `${issue.keys.length > 1 ? "Okända nycklar" : "Okänd nyckel"}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Ogiltig nyckel i ${issue.origin ?? "värdet"}`;
		            case "invalid_union":
		                return "Ogiltig input";
		            case "invalid_element":
		                return `Ogiltigt värde i ${issue.origin ?? "värdet"}`;
		            default:
		                return `Ogiltig input`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (sv$1, sv$1.exports));
	return sv$1.exports;
}

var ta$1 = {exports: {}};

var ta = ta$1.exports;

var hasRequiredTa;

function requireTa () {
	if (hasRequiredTa) return ta$1.exports;
	hasRequiredTa = 1;
	(function (module, exports) {
		var __createBinding = (ta && ta.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ta && ta.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ta && ta.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "எழுத்துக்கள்", verb: "கொண்டிருக்க வேண்டும்" },
		        file: { unit: "பைட்டுகள்", verb: "கொண்டிருக்க வேண்டும்" },
		        array: { unit: "உறுப்புகள்", verb: "கொண்டிருக்க வேண்டும்" },
		        set: { unit: "உறுப்புகள்", verb: "கொண்டிருக்க வேண்டும்" },
		        map: { unit: "உறுப்புகள்", verb: "கொண்டிருக்க வேண்டும்" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "உள்ளீடு",
		        email: "மின்னஞ்சல் முகவரி",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO தேதி நேரம்",
		        date: "ISO தேதி",
		        time: "ISO நேரம்",
		        duration: "ISO கால அளவு",
		        ipv4: "IPv4 முகவரி",
		        ipv6: "IPv6 முகவரி",
		        mac: "MAC முகவரி",
		        cidrv4: "IPv4 வரம்பு",
		        cidrv6: "IPv6 வரம்பு",
		        base64: "base64-encoded சரம்",
		        base64url: "base64url-encoded சரம்",
		        json_string: "JSON சரம்",
		        e164: "E.164 எண்",
		        credit_card: "கடன் அட்டை எண்",
		        currency_code: "நாணயக் குறியீடு",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "input",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "எண்",
		        array: "அணி",
		        null: "வெறுமை",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `தவறான உள்ளீடு: எதிர்பார்க்கப்பட்டது instanceof ${issue.expected}, பெறப்பட்டது ${received}`;
		                }
		                return `தவறான உள்ளீடு: எதிர்பார்க்கப்பட்டது ${expected}, பெறப்பட்டது ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `தவறான உள்ளீடு: எதிர்பார்க்கப்பட்டது ${util.stringifyPrimitive(issue.values[0])}`;
		                return `தவறான விருப்பம்: எதிர்பார்க்கப்பட்டது ${util.joinValues(issue.values, "|")} இல் ஒன்று`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `மிக பெரியது: எதிர்பார்க்கப்பட்டது ${issue.origin ?? "மதிப்பு"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "உறுப்புகள்"} ஆக இருக்க வேண்டும்`;
		                }
		                return `மிக பெரியது: எதிர்பார்க்கப்பட்டது ${issue.origin ?? "மதிப்பு"} ${adj}${issue.maximum.toString()} ஆக இருக்க வேண்டும்`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `மிகச் சிறியது: எதிர்பார்க்கப்பட்டது ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} ஆக இருக்க வேண்டும்`; //
		                }
		                return `மிகச் சிறியது: எதிர்பார்க்கப்பட்டது ${issue.origin} ${adj}${issue.minimum.toString()} ஆக இருக்க வேண்டும்`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `தவறான சரம்: "${_issue.prefix}" இல் தொடங்க வேண்டும்`;
		                if (_issue.format === "ends_with")
		                    return `தவறான சரம்: "${_issue.suffix}" இல் முடிவடைய வேண்டும்`;
		                if (_issue.format === "includes")
		                    return `தவறான சரம்: "${_issue.includes}" ஐ உள்ளடக்க வேண்டும்`;
		                if (_issue.format === "regex")
		                    return `தவறான சரம்: ${_issue.pattern} முறைபாட்டுடன் பொருந்த வேண்டும்`;
		                return `தவறான ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `தவறான எண்: ${issue.divisor} இன் பலமாக இருக்க வேண்டும்`;
		            case "unrecognized_keys":
		                return `அடையாளம் தெரியாத விசை${issue.keys.length > 1 ? "கள்" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} இல் தவறான விசை`;
		            case "invalid_union":
		                return "தவறான உள்ளீடு";
		            case "invalid_element":
		                return `${issue.origin} இல் தவறான மதிப்பு`;
		            default:
		                return `தவறான உள்ளீடு`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ta$1, ta$1.exports));
	return ta$1.exports;
}

var tg$1 = {exports: {}};

var tg = tg$1.exports;

var hasRequiredTg;

function requireTg () {
	if (hasRequiredTg) return tg$1.exports;
	hasRequiredTg = 1;
	(function (module, exports) {
		var __createBinding = (tg && tg.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (tg && tg.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (tg && tg.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    // singular units after numerals in Tajik
		    const Sizable = {
		        string: { unit: "аломат", verb: "дошта бошад" },
		        file: { unit: "байт", verb: "дошта бошад" },
		        array: { unit: "унсур", verb: "дошта бошад" },
		        set: { unit: "унсур", verb: "дошта бошад" },
		        map: { unit: "сабт", verb: "дошта бошад" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "вуруд",
		        email: "суроғаи email",
		        url: "URL",
		        emoji: "эмоҷи",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "санаву вақти ISO",
		        date: "санаи ISO",
		        time: "вақти ISO",
		        duration: "давомнокии ISO",
		        ipv4: "суроғаи IPv4",
		        ipv6: "суроғаи IPv6",
		        mac: "суроғаи MAC",
		        cidrv4: "маҳдудаи IPv4",
		        cidrv6: "маҳдудаи IPv6",
		        base64: "сатри дар формати base64",
		        base64url: "сатри дар формати base64url",
		        json_string: "сатри JSON",
		        e164: "рақами E.164",
		        credit_card: "рақами корти кредитӣ",
		        currency_code: "рамзи асъор",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "вуруд",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "рақам",
		        string: "сатр",
		        array: "массив",
		        object: "объект",
		        date: "сана",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `Вуруди нодуруст: ${expected} интизор мерафт, ${received} гирифта шуд`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Вуруди нодуруст: ${util.stringifyPrimitive(issue.values[0])} интизор мерафт`;
		                return `Интихоби нодуруст: яке аз ${util.joinValues(issue.values, "|")} интизор мерафт`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Хеле калон: ${issue.origin ?? "қимат"} бояд ${adj}${issue.maximum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `Хеле калон: ${issue.origin ?? "қимат"} бояд ${adj}${issue.maximum.toString()} бошад`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Хеле хурд: ${issue.origin} бояд ${adj}${issue.minimum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `Хеле хурд: ${issue.origin} бояд ${adj}${issue.minimum.toString()} бошад`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Сатри нодуруст: бояд бо "${_issue.prefix}" оғоз шавад`;
		                if (_issue.format === "ends_with")
		                    return `Сатри нодуруст: бояд бо "${_issue.suffix}" анҷом ёбад`;
		                if (_issue.format === "includes")
		                    return `Сатри нодуруст: бояд "${_issue.includes}"-ро дар бар гирад`;
		                if (_issue.format === "regex")
		                    return `Сатри нодуруст: бояд ба намунаи ${_issue.pattern} мувофиқат кунад`;
		                return `${FormatDictionary[_issue.format] ?? issue.format}-и нодуруст`;
		            }
		            case "not_multiple_of":
		                return `Рақами нодуруст: бояд ба ${issue.divisor} бе бақия тақсим шавад`;
		            case "unrecognized_keys":
		                return `Калид${issue.keys.length > 1 ? "ҳои" : "и"} номаълум: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Калиди нодуруст дар ${issue.origin}`;
		            case "invalid_union":
		                if (issue.options && Array.isArray(issue.options) && issue.options.length > 0) {
		                    const opts = issue.options.map((o) => `'${o}'`).join(" | ");
		                    return `Қимати нодурусти дискриминатор: ${opts} интизор мерафт`;
		                }
		                return "Вуруди нодуруст";
		            case "invalid_element":
		                return `Қимати нодуруст дар ${issue.origin}`;
		            default:
		                return `Вуруди нодуруст`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (tg$1, tg$1.exports));
	return tg$1.exports;
}

var th$1 = {exports: {}};

var th = th$1.exports;

var hasRequiredTh;

function requireTh () {
	if (hasRequiredTh) return th$1.exports;
	hasRequiredTh = 1;
	(function (module, exports) {
		var __createBinding = (th && th.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (th && th.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (th && th.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "ตัวอักษร", verb: "ควรมี" },
		        file: { unit: "ไบต์", verb: "ควรมี" },
		        array: { unit: "รายการ", verb: "ควรมี" },
		        set: { unit: "รายการ", verb: "ควรมี" },
		        map: { unit: "รายการ", verb: "ควรมี" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ข้อมูลที่ป้อน",
		        email: "ที่อยู่อีเมล",
		        url: "URL",
		        emoji: "อิโมจิ",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "วันที่เวลาแบบ ISO",
		        date: "วันที่แบบ ISO",
		        time: "เวลาแบบ ISO",
		        duration: "ช่วงเวลาแบบ ISO",
		        ipv4: "ที่อยู่ IPv4",
		        ipv6: "ที่อยู่ IPv6",
		        mac: "ที่อยู่ MAC",
		        cidrv4: "ช่วง IP แบบ IPv4",
		        cidrv6: "ช่วง IP แบบ IPv6",
		        base64: "ข้อความแบบ Base64",
		        base64url: "ข้อความแบบ Base64 สำหรับ URL",
		        json_string: "ข้อความแบบ JSON",
		        e164: "เบอร์โทรศัพท์ระหว่างประเทศ (E.164)",
		        credit_card: "หมายเลขบัตรเครดิต",
		        currency_code: "รหัสสกุลเงิน",
		        iban: "IBAN",
		        jwt: "โทเคน JWT",
		        template_literal: "ข้อมูลที่ป้อน",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "ตัวเลข",
		        array: "อาร์เรย์ (Array)",
		        null: "ไม่มีค่า (null)",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `ประเภทข้อมูลไม่ถูกต้อง: ควรเป็น instanceof ${issue.expected} แต่ได้รับ ${received}`;
		                }
		                return `ประเภทข้อมูลไม่ถูกต้อง: ควรเป็น ${expected} แต่ได้รับ ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `ค่าไม่ถูกต้อง: ควรเป็น ${util.stringifyPrimitive(issue.values[0])}`;
		                return `ตัวเลือกไม่ถูกต้อง: ควรเป็นหนึ่งใน ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "ไม่เกิน" : "น้อยกว่า";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `เกินกำหนด: ${issue.origin ?? "ค่า"} ควรมี${adj} ${issue.maximum.toString()} ${sizing.unit ?? "รายการ"}`;
		                return `เกินกำหนด: ${issue.origin ?? "ค่า"} ควรมี${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? "อย่างน้อย" : "มากกว่า";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `น้อยกว่ากำหนด: ${issue.origin} ควรมี${adj} ${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `น้อยกว่ากำหนด: ${issue.origin} ควรมี${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `รูปแบบไม่ถูกต้อง: ข้อความต้องขึ้นต้นด้วย "${_issue.prefix}"`;
		                }
		                if (_issue.format === "ends_with")
		                    return `รูปแบบไม่ถูกต้อง: ข้อความต้องลงท้ายด้วย "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `รูปแบบไม่ถูกต้อง: ข้อความต้องมี "${_issue.includes}" อยู่ในข้อความ`;
		                if (_issue.format === "regex")
		                    return `รูปแบบไม่ถูกต้อง: ต้องตรงกับรูปแบบที่กำหนด ${_issue.pattern}`;
		                return `รูปแบบไม่ถูกต้อง: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `ตัวเลขไม่ถูกต้อง: ต้องเป็นจำนวนที่หารด้วย ${issue.divisor} ได้ลงตัว`;
		            case "unrecognized_keys":
		                return `พบคีย์ที่ไม่รู้จัก: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `คีย์ไม่ถูกต้องใน ${issue.origin}`;
		            case "invalid_union":
		                return "ข้อมูลไม่ถูกต้อง: ไม่ตรงกับรูปแบบยูเนียนที่กำหนดไว้";
		            case "invalid_element":
		                return `ข้อมูลไม่ถูกต้องใน ${issue.origin}`;
		            default:
		                return `ข้อมูลไม่ถูกต้อง`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (th$1, th$1.exports));
	return th$1.exports;
}

var tk$1 = {exports: {}};

var tk = tk$1.exports;

var hasRequiredTk;

function requireTk () {
	if (hasRequiredTk) return tk$1.exports;
	hasRequiredTk = 1;
	(function (module, exports) {
		var __createBinding = (tk && tk.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (tk && tk.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (tk && tk.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "simwol", verb: "bolmaly" },
		        file: { unit: "baýt", verb: "bolmaly" },
		        array: { unit: "elementler", verb: "bolmaly" },
		        set: { unit: "elementler", verb: "bolmaly" },
		        map: { unit: "elementler", verb: "bolmaly" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "giriş",
		        email: "e-poçta salgysy",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO sene we wagt",
		        date: "ISO sene",
		        time: "ISO wagt",
		        duration: "ISO wagt aralygy",
		        ipv4: "IPv4 salgysy",
		        ipv6: "IPv6 salgysy",
		        mac: "MAC salgysy",
		        cidrv4: "IPv4 aralygy",
		        cidrv6: "IPv6 aralygy",
		        base64: "base64 bilen şifrlenen setir",
		        base64url: "base64url bilen şifrlenen setir",
		        json_string: "JSON setiri",
		        e164: "E.164 nomeri",
		        credit_card: "kredit kartynyň nomeri",
		        currency_code: "walýuta kody",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "şablon",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                return `Nädogry baha: garaşylan ${expected} ýerine ${received} alyndy`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Nädogry baha: ${util.stringifyPrimitive(issue.values[0])} bolmaly`;
		                return `Nädogry saýlaw: aşakdakylardan biri bolmaly: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Has uly: garaşylýan ${issue.origin ?? "baha"} ${adj} ${issue.maximum.toString()} ${sizing.unit ?? "element"}`;
		                return `Has uly: garaşylýan ${issue.origin ?? "baha"} ${adj} ${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Has kiçi: garaşylýan ${issue.origin} ${adj} ${issue.minimum.toString()} ${sizing.unit}`;
		                return `Has kiçi: garaşylýan ${issue.origin} ${adj} ${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Nädogry setir: "${_issue.prefix}" bilen başlamaly`;
		                if (_issue.format === "ends_with")
		                    return `Nädogry setir: "${_issue.suffix}" bilen gutarmaly`;
		                if (_issue.format === "includes")
		                    return `Nädogry setir: "${_issue.includes}" saklamaly`;
		                if (_issue.format === "regex")
		                    return `Nädogry setir: ${_issue.pattern} nusga laýyk bolmaly`;
		                return `Nädogry ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Nädogry san: ${issue.divisor} bilen galyndysyz bölünmeli`;
		            case "unrecognized_keys":
		                return `Tanalmaýan açar${issue.keys.length > 1 ? "lar" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} içinde nädogry açar`;
		            case "invalid_union":
		                return "Nädogry baha";
		            case "invalid_element":
		                return `${issue.origin} içinde nädogry baha`;
		            default:
		                return `Nädogry baha`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (tk$1, tk$1.exports));
	return tk$1.exports;
}

var tr$1 = {exports: {}};

var tr = tr$1.exports;

var hasRequiredTr;

function requireTr () {
	if (hasRequiredTr) return tr$1.exports;
	hasRequiredTr = 1;
	(function (module, exports) {
		var __createBinding = (tr && tr.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (tr && tr.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (tr && tr.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "karakter", verb: "olmalı" },
		        file: { unit: "bayt", verb: "olmalı" },
		        array: { unit: "öğe", verb: "olmalı" },
		        set: { unit: "öğe", verb: "olmalı" },
		        map: { unit: "öğe", verb: "olmalı" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "girdi",
		        email: "e-posta adresi",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO tarih ve saat",
		        date: "ISO tarih",
		        time: "ISO saat",
		        duration: "ISO süre",
		        ipv4: "IPv4 adresi",
		        ipv6: "IPv6 adresi",
		        mac: "MAC adresi",
		        cidrv4: "IPv4 aralığı",
		        cidrv6: "IPv6 aralığı",
		        base64: "base64 ile şifrelenmiş metin",
		        base64url: "base64url ile şifrelenmiş metin",
		        json_string: "JSON dizesi",
		        e164: "E.164 sayısı",
		        credit_card: "kredi kartı numarası",
		        currency_code: "para birimi kodu",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "Şablon dizesi",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Geçersiz değer: beklenen instanceof ${issue.expected}, alınan ${received}`;
		                }
		                return `Geçersiz değer: beklenen ${expected}, alınan ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Geçersiz değer: beklenen ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Geçersiz seçenek: aşağıdakilerden biri olmalı: ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Çok büyük: beklenen ${issue.origin ?? "değer"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "öğe"}`;
		                return `Çok büyük: beklenen ${issue.origin ?? "değer"} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Çok küçük: beklenen ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                return `Çok küçük: beklenen ${issue.origin} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Geçersiz metin: "${_issue.prefix}" ile başlamalı`;
		                if (_issue.format === "ends_with")
		                    return `Geçersiz metin: "${_issue.suffix}" ile bitmeli`;
		                if (_issue.format === "includes")
		                    return `Geçersiz metin: "${_issue.includes}" içermeli`;
		                if (_issue.format === "regex")
		                    return `Geçersiz metin: ${_issue.pattern} desenine uymalı`;
		                return `Geçersiz ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Geçersiz sayı: ${issue.divisor} ile tam bölünebilmeli`;
		            case "unrecognized_keys":
		                return `Tanınmayan anahtar${issue.keys.length > 1 ? "lar" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} içinde geçersiz anahtar`;
		            case "invalid_union":
		                return "Geçersiz değer";
		            case "invalid_element":
		                return `${issue.origin} içinde geçersiz değer`;
		            default:
		                return `Geçersiz değer`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (tr$1, tr$1.exports));
	return tr$1.exports;
}

var ua$1 = {exports: {}};

var uk$1 = {exports: {}};

var uk = uk$1.exports;

var hasRequiredUk;

function requireUk () {
	if (hasRequiredUk) return uk$1.exports;
	hasRequiredUk = 1;
	(function (module, exports) {
		var __createBinding = (uk && uk.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (uk && uk.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (uk && uk.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "символів", verb: "матиме" },
		        file: { unit: "байтів", verb: "матиме" },
		        array: { unit: "елементів", verb: "матиме" },
		        set: { unit: "елементів", verb: "матиме" },
		        map: { unit: "елементів", verb: "матиме" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "вхідні дані",
		        email: "адреса електронної пошти",
		        url: "URL",
		        emoji: "емодзі",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "дата та час ISO",
		        date: "дата ISO",
		        time: "час ISO",
		        duration: "тривалість ISO",
		        ipv4: "адреса IPv4",
		        ipv6: "адреса IPv6",
		        mac: "адреса MAC",
		        cidrv4: "діапазон IPv4",
		        cidrv6: "діапазон IPv6",
		        base64: "рядок у кодуванні base64",
		        base64url: "рядок у кодуванні base64url",
		        json_string: "рядок JSON",
		        e164: "номер E.164",
		        credit_card: "номер кредитної картки",
		        currency_code: "код валюти",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "вхідні дані",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "число",
		        array: "масив",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Неправильні вхідні дані: очікується instanceof ${issue.expected}, отримано ${received}`;
		                }
		                return `Неправильні вхідні дані: очікується ${expected}, отримано ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Неправильні вхідні дані: очікується ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Неправильна опція: очікується одне з ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Занадто велике: очікується, що ${issue.origin ?? "значення"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "елементів"}`;
		                return `Занадто велике: очікується, що ${issue.origin ?? "значення"} буде ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Занадто мале: очікується, що ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Занадто мале: очікується, що ${issue.origin} буде ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Неправильний рядок: повинен починатися з "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Неправильний рядок: повинен закінчуватися на "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Неправильний рядок: повинен містити "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Неправильний рядок: повинен відповідати шаблону ${_issue.pattern}`;
		                return `Неправильний ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Неправильне число: повинно бути кратним ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Нерозпізнаний ключ${issue.keys.length > 1 ? "і" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Неправильний ключ у ${issue.origin}`;
		            case "invalid_union":
		                return "Неправильні вхідні дані";
		            case "invalid_element":
		                return `Неправильне значення у ${issue.origin}`;
		            default:
		                return `Неправильні вхідні дані`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (uk$1, uk$1.exports));
	return uk$1.exports;
}

var ua = ua$1.exports;

var hasRequiredUa;

function requireUa () {
	if (hasRequiredUa) return ua$1.exports;
	hasRequiredUa = 1;
	(function (module, exports) {
		var __importDefault = (ua && ua.__importDefault) || function (mod) {
		    return (mod && mod.__esModule) ? mod : { "default": mod };
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const uk_js_1 = __importDefault(/*@__PURE__*/ requireUk());
		/** @deprecated Use `uk` instead. */
		function default_1() {
		    return (0, uk_js_1.default)();
		}
		module.exports = exports.default; 
	} (ua$1, ua$1.exports));
	return ua$1.exports;
}

var ur$1 = {exports: {}};

var ur = ur$1.exports;

var hasRequiredUr;

function requireUr () {
	if (hasRequiredUr) return ur$1.exports;
	hasRequiredUr = 1;
	(function (module, exports) {
		var __createBinding = (ur && ur.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (ur && ur.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (ur && ur.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "حروف", verb: "ہونا" },
		        file: { unit: "بائٹس", verb: "ہونا" },
		        array: { unit: "آئٹمز", verb: "ہونا" },
		        set: { unit: "آئٹمز", verb: "ہونا" },
		        map: { unit: "آئٹمز", verb: "ہونا" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ان پٹ",
		        email: "ای میل ایڈریس",
		        url: "یو آر ایل",
		        emoji: "ایموجی",
		        uuid: "یو یو آئی ڈی",
		        uuidv4: "یو یو آئی ڈی وی 4",
		        uuidv6: "یو یو آئی ڈی وی 6",
		        nanoid: "نینو آئی ڈی",
		        guid: "جی یو آئی ڈی",
		        cuid: "سی یو آئی ڈی",
		        cuid2: "سی یو آئی ڈی 2",
		        ulid: "یو ایل آئی ڈی",
		        xid: "ایکس آئی ڈی",
		        ksuid: "کے ایس یو آئی ڈی",
		        datetime: "آئی ایس او ڈیٹ ٹائم",
		        date: "آئی ایس او تاریخ",
		        time: "آئی ایس او وقت",
		        duration: "آئی ایس او مدت",
		        ipv4: "آئی پی وی 4 ایڈریس",
		        ipv6: "آئی پی وی 6 ایڈریس",
		        mac: "ایم اے سی ایڈریس",
		        cidrv4: "آئی پی وی 4 رینج",
		        cidrv6: "آئی پی وی 6 رینج",
		        base64: "بیس 64 ان کوڈڈ سٹرنگ",
		        base64url: "بیس 64 یو آر ایل ان کوڈڈ سٹرنگ",
		        json_string: "جے ایس او این سٹرنگ",
		        e164: "ای 164 نمبر",
		        credit_card: "کریڈٹ کارڈ نمبر",
		        currency_code: "کرنسی کوڈ",
		        iban: "IBAN",
		        jwt: "جے ڈبلیو ٹی",
		        template_literal: "ان پٹ",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "نمبر",
		        array: "آرے",
		        null: "نل",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `غلط ان پٹ: instanceof ${issue.expected} متوقع تھا، ${received} موصول ہوا`;
		                }
		                return `غلط ان پٹ: ${expected} متوقع تھا، ${received} موصول ہوا`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `غلط ان پٹ: ${util.stringifyPrimitive(issue.values[0])} متوقع تھا`;
		                return `غلط آپشن: ${util.joinValues(issue.values, "|")} میں سے ایک متوقع تھا`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `بہت بڑا: ${issue.origin ?? "ویلیو"} کے ${adj}${issue.maximum.toString()} ${sizing.unit ?? "عناصر"} ہونے متوقع تھے`;
		                return `بہت بڑا: ${issue.origin ?? "ویلیو"} کا ${adj}${issue.maximum.toString()} ہونا متوقع تھا`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `بہت چھوٹا: ${issue.origin} کے ${adj}${issue.minimum.toString()} ${sizing.unit} ہونے متوقع تھے`;
		                }
		                return `بہت چھوٹا: ${issue.origin} کا ${adj}${issue.minimum.toString()} ہونا متوقع تھا`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `غلط سٹرنگ: "${_issue.prefix}" سے شروع ہونا چاہیے`;
		                }
		                if (_issue.format === "ends_with")
		                    return `غلط سٹرنگ: "${_issue.suffix}" پر ختم ہونا چاہیے`;
		                if (_issue.format === "includes")
		                    return `غلط سٹرنگ: "${_issue.includes}" شامل ہونا چاہیے`;
		                if (_issue.format === "regex")
		                    return `غلط سٹرنگ: پیٹرن ${_issue.pattern} سے میچ ہونا چاہیے`;
		                return `غلط ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `غلط نمبر: ${issue.divisor} کا مضاعف ہونا چاہیے`;
		            case "unrecognized_keys":
		                return `غیر تسلیم شدہ کی${issue.keys.length > 1 ? "ز" : ""}: ${util.joinValues(issue.keys, "، ")}`;
		            case "invalid_key":
		                return `${issue.origin} میں غلط کی`;
		            case "invalid_union":
		                return "غلط ان پٹ";
		            case "invalid_element":
		                return `${issue.origin} میں غلط ویلیو`;
		            default:
		                return `غلط ان پٹ`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (ur$1, ur$1.exports));
	return ur$1.exports;
}

var uz$1 = {exports: {}};

var uz = uz$1.exports;

var hasRequiredUz;

function requireUz () {
	if (hasRequiredUz) return uz$1.exports;
	hasRequiredUz = 1;
	(function (module, exports) {
		var __createBinding = (uz && uz.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (uz && uz.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (uz && uz.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "belgi", verb: "bo‘lishi kerak" },
		        file: { unit: "bayt", verb: "bo‘lishi kerak" },
		        array: { unit: "element", verb: "bo‘lishi kerak" },
		        set: { unit: "element", verb: "bo‘lishi kerak" },
		        map: { unit: "yozuv", verb: "bo‘lishi kerak" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "kirish",
		        email: "elektron pochta manzili",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO sana va vaqti",
		        date: "ISO sana",
		        time: "ISO vaqt",
		        duration: "ISO davomiylik",
		        ipv4: "IPv4 manzil",
		        ipv6: "IPv6 manzil",
		        mac: "MAC manzil",
		        cidrv4: "IPv4 diapazon",
		        cidrv6: "IPv6 diapazon",
		        base64: "base64 kodlangan satr",
		        base64url: "base64url kodlangan satr",
		        json_string: "JSON satr",
		        e164: "E.164 raqam",
		        credit_card: "kredit karta raqami",
		        currency_code: "valyuta kodi",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "kirish",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "raqam",
		        array: "massiv",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Noto‘g‘ri kirish: kutilgan instanceof ${issue.expected}, qabul qilingan ${received}`;
		                }
		                return `Noto‘g‘ri kirish: kutilgan ${expected}, qabul qilingan ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Noto‘g‘ri kirish: kutilgan ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Noto‘g‘ri variant: quyidagilardan biri kutilgan ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Juda katta: kutilgan ${issue.origin ?? "qiymat"} ${adj}${issue.maximum.toString()} ${sizing.unit} ${sizing.verb}`;
		                return `Juda katta: kutilgan ${issue.origin ?? "qiymat"} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Juda kichik: kutilgan ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit} ${sizing.verb}`;
		                }
		                return `Juda kichik: kutilgan ${issue.origin} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Noto‘g‘ri satr: "${_issue.prefix}" bilan boshlanishi kerak`;
		                if (_issue.format === "ends_with")
		                    return `Noto‘g‘ri satr: "${_issue.suffix}" bilan tugashi kerak`;
		                if (_issue.format === "includes")
		                    return `Noto‘g‘ri satr: "${_issue.includes}" ni o‘z ichiga olishi kerak`;
		                if (_issue.format === "regex")
		                    return `Noto‘g‘ri satr: ${_issue.pattern} shabloniga mos kelishi kerak`;
		                return `Noto‘g‘ri ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Noto‘g‘ri raqam: ${issue.divisor} ning karralisi bo‘lishi kerak`;
		            case "unrecognized_keys":
		                return `Noma’lum kalit${issue.keys.length > 1 ? "lar" : ""}: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} dagi kalit noto‘g‘ri`;
		            case "invalid_union":
		                return "Noto‘g‘ri kirish";
		            case "invalid_element":
		                return `${issue.origin} da noto‘g‘ri qiymat`;
		            default:
		                return `Noto‘g‘ri kirish`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (uz$1, uz$1.exports));
	return uz$1.exports;
}

var vi$1 = {exports: {}};

var vi = vi$1.exports;

var hasRequiredVi;

function requireVi () {
	if (hasRequiredVi) return vi$1.exports;
	hasRequiredVi = 1;
	(function (module, exports) {
		var __createBinding = (vi && vi.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (vi && vi.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (vi && vi.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "ký tự", verb: "có" },
		        file: { unit: "byte", verb: "có" },
		        array: { unit: "phần tử", verb: "có" },
		        set: { unit: "phần tử", verb: "có" },
		        map: { unit: "phần tử", verb: "có" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "đầu vào",
		        email: "địa chỉ email",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ngày giờ ISO",
		        date: "ngày ISO",
		        time: "giờ ISO",
		        duration: "khoảng thời gian ISO",
		        ipv4: "địa chỉ IPv4",
		        ipv6: "địa chỉ IPv6",
		        mac: "địa chỉ MAC",
		        cidrv4: "dải IPv4",
		        cidrv6: "dải IPv6",
		        base64: "chuỗi mã hóa base64",
		        base64url: "chuỗi mã hóa base64url",
		        json_string: "chuỗi JSON",
		        e164: "số E.164",
		        credit_card: "số thẻ tín dụng",
		        currency_code: "mã tiền tệ",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "đầu vào",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "số",
		        array: "mảng",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Đầu vào không hợp lệ: mong đợi instanceof ${issue.expected}, nhận được ${received}`;
		                }
		                return `Đầu vào không hợp lệ: mong đợi ${expected}, nhận được ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Đầu vào không hợp lệ: mong đợi ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Tùy chọn không hợp lệ: mong đợi một trong các giá trị ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Quá lớn: mong đợi ${issue.origin ?? "giá trị"} ${sizing.verb} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "phần tử"}`;
		                return `Quá lớn: mong đợi ${issue.origin ?? "giá trị"} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `Quá nhỏ: mong đợi ${issue.origin} ${sizing.verb} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `Quá nhỏ: mong đợi ${issue.origin} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Chuỗi không hợp lệ: phải bắt đầu bằng "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Chuỗi không hợp lệ: phải kết thúc bằng "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Chuỗi không hợp lệ: phải bao gồm "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Chuỗi không hợp lệ: phải khớp với mẫu ${_issue.pattern}`;
		                return `${FormatDictionary[_issue.format] ?? issue.format} không hợp lệ`;
		            }
		            case "not_multiple_of":
		                return `Số không hợp lệ: phải là bội số của ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Khóa không được nhận dạng: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Khóa không hợp lệ trong ${issue.origin}`;
		            case "invalid_union":
		                return "Đầu vào không hợp lệ";
		            case "invalid_element":
		                return `Giá trị không hợp lệ trong ${issue.origin}`;
		            default:
		                return `Đầu vào không hợp lệ`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (vi$1, vi$1.exports));
	return vi$1.exports;
}

var zhCN$1 = {exports: {}};

var zhCN = zhCN$1.exports;

var hasRequiredZhCN;

function requireZhCN () {
	if (hasRequiredZhCN) return zhCN$1.exports;
	hasRequiredZhCN = 1;
	(function (module, exports) {
		var __createBinding = (zhCN && zhCN.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (zhCN && zhCN.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (zhCN && zhCN.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "字符", verb: "包含" },
		        file: { unit: "字节", verb: "包含" },
		        array: { unit: "项", verb: "包含" },
		        set: { unit: "项", verb: "包含" },
		        map: { unit: "项", verb: "包含" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "输入",
		        email: "电子邮件",
		        url: "URL",
		        emoji: "表情符号",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO日期时间",
		        date: "ISO日期",
		        time: "ISO时间",
		        duration: "ISO时长",
		        ipv4: "IPv4地址",
		        ipv6: "IPv6地址",
		        mac: "MAC地址",
		        cidrv4: "IPv4网段",
		        cidrv6: "IPv6网段",
		        base64: "base64编码字符串",
		        base64url: "base64url编码字符串",
		        json_string: "JSON字符串",
		        e164: "E.164号码",
		        credit_card: "信用卡号",
		        currency_code: "货币代码",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "输入",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "数字",
		        array: "数组",
		        null: "空值(null)",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `无效输入：期望 instanceof ${issue.expected}，实际接收 ${received}`;
		                }
		                return `无效输入：期望 ${expected}，实际接收 ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `无效输入：期望 ${util.stringifyPrimitive(issue.values[0])}`;
		                return `无效选项：期望以下之一 ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `数值过大：期望 ${issue.origin ?? "值"} ${adj}${issue.maximum.toString()} ${sizing.unit ?? "个元素"}`;
		                return `数值过大：期望 ${issue.origin ?? "值"} ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `数值过小：期望 ${issue.origin} ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `数值过小：期望 ${issue.origin} ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `无效字符串：必须以 "${_issue.prefix}" 开头`;
		                if (_issue.format === "ends_with")
		                    return `无效字符串：必须以 "${_issue.suffix}" 结尾`;
		                if (_issue.format === "includes")
		                    return `无效字符串：必须包含 "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `无效字符串：必须满足正则表达式 ${_issue.pattern}`;
		                return `无效${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `无效数字：必须是 ${issue.divisor} 的倍数`;
		            case "unrecognized_keys":
		                return `出现未知的键(key): ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `${issue.origin} 中的键(key)无效`;
		            case "invalid_union":
		                return "无效输入";
		            case "invalid_element":
		                return `${issue.origin} 中包含无效值(value)`;
		            default:
		                return `无效输入`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (zhCN$1, zhCN$1.exports));
	return zhCN$1.exports;
}

var zhTW$1 = {exports: {}};

var zhTW = zhTW$1.exports;

var hasRequiredZhTW;

function requireZhTW () {
	if (hasRequiredZhTW) return zhTW$1.exports;
	hasRequiredZhTW = 1;
	(function (module, exports) {
		var __createBinding = (zhTW && zhTW.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (zhTW && zhTW.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (zhTW && zhTW.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "字元", verb: "擁有" },
		        file: { unit: "位元組", verb: "擁有" },
		        array: { unit: "項目", verb: "擁有" },
		        set: { unit: "項目", verb: "擁有" },
		        map: { unit: "項目", verb: "擁有" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "輸入",
		        email: "郵件地址",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "ISO 日期時間",
		        date: "ISO 日期",
		        time: "ISO 時間",
		        duration: "ISO 期間",
		        ipv4: "IPv4 位址",
		        ipv6: "IPv6 位址",
		        mac: "MAC 位址",
		        cidrv4: "IPv4 範圍",
		        cidrv6: "IPv6 範圍",
		        base64: "base64 編碼字串",
		        base64url: "base64url 編碼字串",
		        json_string: "JSON 字串",
		        e164: "E.164 數值",
		        credit_card: "信用卡號",
		        currency_code: "貨幣代碼",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "輸入",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `無效的輸入值：預期為 instanceof ${issue.expected}，但收到 ${received}`;
		                }
		                return `無效的輸入值：預期為 ${expected}，但收到 ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `無效的輸入值：預期為 ${util.stringifyPrimitive(issue.values[0])}`;
		                return `無效的選項：預期為以下其中之一 ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `數值過大：預期 ${issue.origin ?? "值"} 應為 ${adj}${issue.maximum.toString()} ${sizing.unit ?? "個元素"}`;
		                return `數值過大：預期 ${issue.origin ?? "值"} 應為 ${adj}${issue.maximum.toString()}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing) {
		                    return `數值過小：預期 ${issue.origin} 應為 ${adj}${issue.minimum.toString()} ${sizing.unit}`;
		                }
		                return `數值過小：預期 ${issue.origin} 應為 ${adj}${issue.minimum.toString()}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with") {
		                    return `無效的字串：必須以 "${_issue.prefix}" 開頭`;
		                }
		                if (_issue.format === "ends_with")
		                    return `無效的字串：必須以 "${_issue.suffix}" 結尾`;
		                if (_issue.format === "includes")
		                    return `無效的字串：必須包含 "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `無效的字串：必須符合格式 ${_issue.pattern}`;
		                return `無效的 ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `無效的數字：必須為 ${issue.divisor} 的倍數`;
		            case "unrecognized_keys":
		                return `無法識別的鍵值${issue.keys.length > 1 ? "們" : ""}：${util.joinValues(issue.keys, "、")}`;
		            case "invalid_key":
		                return `${issue.origin} 中有無效的鍵值`;
		            case "invalid_union":
		                return "無效的輸入值";
		            case "invalid_element":
		                return `${issue.origin} 中有無效的值`;
		            default:
		                return `無效的輸入值`;
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (zhTW$1, zhTW$1.exports));
	return zhTW$1.exports;
}

var yo$1 = {exports: {}};

var yo = yo$1.exports;

var hasRequiredYo;

function requireYo () {
	if (hasRequiredYo) return yo$1.exports;
	hasRequiredYo = 1;
	(function (module, exports) {
		var __createBinding = (yo && yo.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (yo && yo.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (yo && yo.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = default_1;
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		const error = () => {
		    const Sizable = {
		        string: { unit: "àmi", verb: "ní" },
		        file: { unit: "bytes", verb: "ní" },
		        array: { unit: "nkan", verb: "ní" },
		        set: { unit: "nkan", verb: "ní" },
		        map: { unit: "nkan", verb: "ní" },
		    };
		    function getSizing(origin) {
		        return Sizable[origin] ?? null;
		    }
		    const FormatDictionary = {
		        regex: "ẹ̀rọ ìbáwọlé",
		        email: "àdírẹ́sì ìmẹ́lì",
		        url: "URL",
		        emoji: "emoji",
		        uuid: "UUID",
		        uuidv4: "UUIDv4",
		        uuidv6: "UUIDv6",
		        nanoid: "nanoid",
		        guid: "GUID",
		        cuid: "cuid",
		        cuid2: "cuid2",
		        ulid: "ULID",
		        xid: "XID",
		        ksuid: "KSUID",
		        datetime: "àkókò ISO",
		        date: "ọjọ́ ISO",
		        time: "àkókò ISO",
		        duration: "àkókò tó pé ISO",
		        ipv4: "àdírẹ́sì IPv4",
		        ipv6: "àdírẹ́sì IPv6",
		        mac: "àdírẹ́sì MAC",
		        cidrv4: "àgbègbè IPv4",
		        cidrv6: "àgbègbè IPv6",
		        base64: "ọ̀rọ̀ tí a kọ́ ní base64",
		        base64url: "ọ̀rọ̀ base64url",
		        json_string: "ọ̀rọ̀ JSON",
		        e164: "nọ́mbà E.164",
		        credit_card: "nọmba kaadi gbese",
		        currency_code: "koodu owó",
		        iban: "IBAN",
		        jwt: "JWT",
		        template_literal: "ẹ̀rọ ìbáwọlé",
		    };
		    const TypeDictionary = {
		        nan: "NaN",
		        number: "nọ́mbà",
		        array: "akopọ",
		    };
		    return (issue) => {
		        switch (issue.code) {
		            case "invalid_type": {
		                const expected = TypeDictionary[issue.expected] ?? issue.expected;
		                const receivedType = util.parsedType(issue.input);
		                const received = TypeDictionary[receivedType] ?? receivedType;
		                if (/^[A-Z]/.test(issue.expected)) {
		                    return `Ìbáwọlé aṣìṣe: a ní láti fi instanceof ${issue.expected}, àmọ̀ a rí ${received}`;
		                }
		                return `Ìbáwọlé aṣìṣe: a ní láti fi ${expected}, àmọ̀ a rí ${received}`;
		            }
		            case "invalid_value":
		                if (issue.values.length === 1)
		                    return `Ìbáwọlé aṣìṣe: a ní láti fi ${util.stringifyPrimitive(issue.values[0])}`;
		                return `Àṣàyàn aṣìṣe: yan ọ̀kan lára ${util.joinValues(issue.values, "|")}`;
		            case "too_big": {
		                const adj = issue.inclusive ? "<=" : "<";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Tó pọ̀ jù: a ní láti jẹ́ pé ${issue.origin ?? "iye"} ${sizing.verb} ${adj}${issue.maximum} ${sizing.unit}`;
		                return `Tó pọ̀ jù: a ní láti jẹ́ ${adj}${issue.maximum}`;
		            }
		            case "too_small": {
		                const adj = issue.inclusive ? ">=" : ">";
		                const sizing = getSizing(issue.origin);
		                if (sizing)
		                    return `Kéré ju: a ní láti jẹ́ pé ${issue.origin} ${sizing.verb} ${adj}${issue.minimum} ${sizing.unit}`;
		                return `Kéré ju: a ní láti jẹ́ ${adj}${issue.minimum}`;
		            }
		            case "invalid_format": {
		                const _issue = issue;
		                if (_issue.format === "starts_with")
		                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ bẹ̀rẹ̀ pẹ̀lú "${_issue.prefix}"`;
		                if (_issue.format === "ends_with")
		                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ parí pẹ̀lú "${_issue.suffix}"`;
		                if (_issue.format === "includes")
		                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ ní "${_issue.includes}"`;
		                if (_issue.format === "regex")
		                    return `Ọ̀rọ̀ aṣìṣe: gbọ́dọ̀ bá àpẹẹrẹ mu ${_issue.pattern}`;
		                return `Aṣìṣe: ${FormatDictionary[_issue.format] ?? issue.format}`;
		            }
		            case "not_multiple_of":
		                return `Nọ́mbà aṣìṣe: gbọ́dọ̀ jẹ́ èyà pípín ti ${issue.divisor}`;
		            case "unrecognized_keys":
		                return `Bọtìnì àìmọ̀: ${util.joinValues(issue.keys, ", ")}`;
		            case "invalid_key":
		                return `Bọtìnì aṣìṣe nínú ${issue.origin}`;
		            case "invalid_union":
		                return "Ìbáwọlé aṣìṣe";
		            case "invalid_element":
		                return `Iye aṣìṣe nínú ${issue.origin}`;
		            default:
		                return "Ìbáwọlé aṣìṣe";
		        }
		    };
		};
		function default_1() {
		    return {
		        localeError: error(),
		    };
		}
		module.exports = exports.default; 
	} (yo$1, yo$1.exports));
	return yo$1.exports;
}

var hasRequiredLocales;

function requireLocales () {
	if (hasRequiredLocales) return locales;
	hasRequiredLocales = 1;
	(function (exports) {
		var __importDefault = (locales && locales.__importDefault) || function (mod) {
		    return (mod && mod.__esModule) ? mod : { "default": mod };
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.sv = exports.sl = exports.sk = exports.ru = exports.ro = exports.ptBR = exports.pt = exports.pl = exports.ps = exports.ota = exports.no = exports.nn = exports.nl = exports.ne = exports.ms = exports.mk = exports.lt = exports.ko = exports.kn = exports.km = exports.kh = exports.ka = exports.ja = exports.it = exports.is = exports.id = exports.hy = exports.hu = exports.hr = exports.hi = exports.he = exports.gu = exports.frCA = exports.fr = exports.fi = exports.fa = exports.es = exports.eo = exports.en = exports.el = exports.de = exports.da = exports.cs = exports.ckb = exports.ca = exports.bn = exports.bg = exports.be = exports.az = exports.ar = void 0;
		exports.yo = exports.zhTW = exports.zhCN = exports.vi = exports.uz = exports.ur = exports.uk = exports.ua = exports.tr = exports.tk = exports.th = exports.tg = exports.ta = void 0;
		var ar_js_1 = /*@__PURE__*/ requireAr();
		Object.defineProperty(exports, "ar", { enumerable: true, get: function () { return __importDefault(ar_js_1).default; } });
		var az_js_1 = /*@__PURE__*/ requireAz();
		Object.defineProperty(exports, "az", { enumerable: true, get: function () { return __importDefault(az_js_1).default; } });
		var be_js_1 = /*@__PURE__*/ requireBe();
		Object.defineProperty(exports, "be", { enumerable: true, get: function () { return __importDefault(be_js_1).default; } });
		var bg_js_1 = /*@__PURE__*/ requireBg();
		Object.defineProperty(exports, "bg", { enumerable: true, get: function () { return __importDefault(bg_js_1).default; } });
		var bn_js_1 = /*@__PURE__*/ requireBn();
		Object.defineProperty(exports, "bn", { enumerable: true, get: function () { return __importDefault(bn_js_1).default; } });
		var ca_js_1 = /*@__PURE__*/ requireCa();
		Object.defineProperty(exports, "ca", { enumerable: true, get: function () { return __importDefault(ca_js_1).default; } });
		var ckb_js_1 = /*@__PURE__*/ requireCkb();
		Object.defineProperty(exports, "ckb", { enumerable: true, get: function () { return __importDefault(ckb_js_1).default; } });
		var cs_js_1 = /*@__PURE__*/ requireCs();
		Object.defineProperty(exports, "cs", { enumerable: true, get: function () { return __importDefault(cs_js_1).default; } });
		var da_js_1 = /*@__PURE__*/ requireDa();
		Object.defineProperty(exports, "da", { enumerable: true, get: function () { return __importDefault(da_js_1).default; } });
		var de_js_1 = /*@__PURE__*/ requireDe();
		Object.defineProperty(exports, "de", { enumerable: true, get: function () { return __importDefault(de_js_1).default; } });
		var el_js_1 = /*@__PURE__*/ requireEl();
		Object.defineProperty(exports, "el", { enumerable: true, get: function () { return __importDefault(el_js_1).default; } });
		var en_js_1 = /*@__PURE__*/ requireEn();
		Object.defineProperty(exports, "en", { enumerable: true, get: function () { return __importDefault(en_js_1).default; } });
		var eo_js_1 = /*@__PURE__*/ requireEo();
		Object.defineProperty(exports, "eo", { enumerable: true, get: function () { return __importDefault(eo_js_1).default; } });
		var es_js_1 = /*@__PURE__*/ requireEs();
		Object.defineProperty(exports, "es", { enumerable: true, get: function () { return __importDefault(es_js_1).default; } });
		var fa_js_1 = /*@__PURE__*/ requireFa();
		Object.defineProperty(exports, "fa", { enumerable: true, get: function () { return __importDefault(fa_js_1).default; } });
		var fi_js_1 = /*@__PURE__*/ requireFi();
		Object.defineProperty(exports, "fi", { enumerable: true, get: function () { return __importDefault(fi_js_1).default; } });
		var fr_js_1 = /*@__PURE__*/ requireFr();
		Object.defineProperty(exports, "fr", { enumerable: true, get: function () { return __importDefault(fr_js_1).default; } });
		var fr_CA_js_1 = /*@__PURE__*/ requireFrCA();
		Object.defineProperty(exports, "frCA", { enumerable: true, get: function () { return __importDefault(fr_CA_js_1).default; } });
		var gu_js_1 = /*@__PURE__*/ requireGu();
		Object.defineProperty(exports, "gu", { enumerable: true, get: function () { return __importDefault(gu_js_1).default; } });
		var he_js_1 = /*@__PURE__*/ requireHe();
		Object.defineProperty(exports, "he", { enumerable: true, get: function () { return __importDefault(he_js_1).default; } });
		var hi_js_1 = /*@__PURE__*/ requireHi();
		Object.defineProperty(exports, "hi", { enumerable: true, get: function () { return __importDefault(hi_js_1).default; } });
		var hr_js_1 = /*@__PURE__*/ requireHr();
		Object.defineProperty(exports, "hr", { enumerable: true, get: function () { return __importDefault(hr_js_1).default; } });
		var hu_js_1 = /*@__PURE__*/ requireHu();
		Object.defineProperty(exports, "hu", { enumerable: true, get: function () { return __importDefault(hu_js_1).default; } });
		var hy_js_1 = /*@__PURE__*/ requireHy();
		Object.defineProperty(exports, "hy", { enumerable: true, get: function () { return __importDefault(hy_js_1).default; } });
		var id_js_1 = /*@__PURE__*/ requireId();
		Object.defineProperty(exports, "id", { enumerable: true, get: function () { return __importDefault(id_js_1).default; } });
		var is_js_1 = /*@__PURE__*/ requireIs$1();
		Object.defineProperty(exports, "is", { enumerable: true, get: function () { return __importDefault(is_js_1).default; } });
		var it_js_1 = /*@__PURE__*/ requireIt();
		Object.defineProperty(exports, "it", { enumerable: true, get: function () { return __importDefault(it_js_1).default; } });
		var ja_js_1 = /*@__PURE__*/ requireJa();
		Object.defineProperty(exports, "ja", { enumerable: true, get: function () { return __importDefault(ja_js_1).default; } });
		var ka_js_1 = /*@__PURE__*/ requireKa();
		Object.defineProperty(exports, "ka", { enumerable: true, get: function () { return __importDefault(ka_js_1).default; } });
		var kh_js_1 = /*@__PURE__*/ requireKh();
		Object.defineProperty(exports, "kh", { enumerable: true, get: function () { return __importDefault(kh_js_1).default; } });
		var km_js_1 = /*@__PURE__*/ requireKm();
		Object.defineProperty(exports, "km", { enumerable: true, get: function () { return __importDefault(km_js_1).default; } });
		var kn_js_1 = /*@__PURE__*/ requireKn();
		Object.defineProperty(exports, "kn", { enumerable: true, get: function () { return __importDefault(kn_js_1).default; } });
		var ko_js_1 = /*@__PURE__*/ requireKo();
		Object.defineProperty(exports, "ko", { enumerable: true, get: function () { return __importDefault(ko_js_1).default; } });
		var lt_js_1 = /*@__PURE__*/ requireLt();
		Object.defineProperty(exports, "lt", { enumerable: true, get: function () { return __importDefault(lt_js_1).default; } });
		var mk_js_1 = /*@__PURE__*/ requireMk();
		Object.defineProperty(exports, "mk", { enumerable: true, get: function () { return __importDefault(mk_js_1).default; } });
		var ms_js_1 = /*@__PURE__*/ requireMs();
		Object.defineProperty(exports, "ms", { enumerable: true, get: function () { return __importDefault(ms_js_1).default; } });
		var ne_js_1 = /*@__PURE__*/ requireNe();
		Object.defineProperty(exports, "ne", { enumerable: true, get: function () { return __importDefault(ne_js_1).default; } });
		var nl_js_1 = /*@__PURE__*/ requireNl();
		Object.defineProperty(exports, "nl", { enumerable: true, get: function () { return __importDefault(nl_js_1).default; } });
		var nn_js_1 = /*@__PURE__*/ requireNn();
		Object.defineProperty(exports, "nn", { enumerable: true, get: function () { return __importDefault(nn_js_1).default; } });
		var no_js_1 = /*@__PURE__*/ requireNo();
		Object.defineProperty(exports, "no", { enumerable: true, get: function () { return __importDefault(no_js_1).default; } });
		var ota_js_1 = /*@__PURE__*/ requireOta();
		Object.defineProperty(exports, "ota", { enumerable: true, get: function () { return __importDefault(ota_js_1).default; } });
		var ps_js_1 = /*@__PURE__*/ requirePs();
		Object.defineProperty(exports, "ps", { enumerable: true, get: function () { return __importDefault(ps_js_1).default; } });
		var pl_js_1 = /*@__PURE__*/ requirePl();
		Object.defineProperty(exports, "pl", { enumerable: true, get: function () { return __importDefault(pl_js_1).default; } });
		var pt_js_1 = /*@__PURE__*/ requirePt();
		Object.defineProperty(exports, "pt", { enumerable: true, get: function () { return __importDefault(pt_js_1).default; } });
		var pt_BR_js_1 = /*@__PURE__*/ requirePtBR();
		Object.defineProperty(exports, "ptBR", { enumerable: true, get: function () { return __importDefault(pt_BR_js_1).default; } });
		var ro_js_1 = /*@__PURE__*/ requireRo();
		Object.defineProperty(exports, "ro", { enumerable: true, get: function () { return __importDefault(ro_js_1).default; } });
		var ru_js_1 = /*@__PURE__*/ requireRu();
		Object.defineProperty(exports, "ru", { enumerable: true, get: function () { return __importDefault(ru_js_1).default; } });
		var sk_js_1 = /*@__PURE__*/ requireSk();
		Object.defineProperty(exports, "sk", { enumerable: true, get: function () { return __importDefault(sk_js_1).default; } });
		var sl_js_1 = /*@__PURE__*/ requireSl();
		Object.defineProperty(exports, "sl", { enumerable: true, get: function () { return __importDefault(sl_js_1).default; } });
		var sv_js_1 = /*@__PURE__*/ requireSv();
		Object.defineProperty(exports, "sv", { enumerable: true, get: function () { return __importDefault(sv_js_1).default; } });
		var ta_js_1 = /*@__PURE__*/ requireTa();
		Object.defineProperty(exports, "ta", { enumerable: true, get: function () { return __importDefault(ta_js_1).default; } });
		var tg_js_1 = /*@__PURE__*/ requireTg();
		Object.defineProperty(exports, "tg", { enumerable: true, get: function () { return __importDefault(tg_js_1).default; } });
		var th_js_1 = /*@__PURE__*/ requireTh();
		Object.defineProperty(exports, "th", { enumerable: true, get: function () { return __importDefault(th_js_1).default; } });
		var tk_js_1 = /*@__PURE__*/ requireTk();
		Object.defineProperty(exports, "tk", { enumerable: true, get: function () { return __importDefault(tk_js_1).default; } });
		var tr_js_1 = /*@__PURE__*/ requireTr();
		Object.defineProperty(exports, "tr", { enumerable: true, get: function () { return __importDefault(tr_js_1).default; } });
		var ua_js_1 = /*@__PURE__*/ requireUa();
		Object.defineProperty(exports, "ua", { enumerable: true, get: function () { return __importDefault(ua_js_1).default; } });
		var uk_js_1 = /*@__PURE__*/ requireUk();
		Object.defineProperty(exports, "uk", { enumerable: true, get: function () { return __importDefault(uk_js_1).default; } });
		var ur_js_1 = /*@__PURE__*/ requireUr();
		Object.defineProperty(exports, "ur", { enumerable: true, get: function () { return __importDefault(ur_js_1).default; } });
		var uz_js_1 = /*@__PURE__*/ requireUz();
		Object.defineProperty(exports, "uz", { enumerable: true, get: function () { return __importDefault(uz_js_1).default; } });
		var vi_js_1 = /*@__PURE__*/ requireVi();
		Object.defineProperty(exports, "vi", { enumerable: true, get: function () { return __importDefault(vi_js_1).default; } });
		var zh_CN_js_1 = /*@__PURE__*/ requireZhCN();
		Object.defineProperty(exports, "zhCN", { enumerable: true, get: function () { return __importDefault(zh_CN_js_1).default; } });
		var zh_TW_js_1 = /*@__PURE__*/ requireZhTW();
		Object.defineProperty(exports, "zhTW", { enumerable: true, get: function () { return __importDefault(zh_TW_js_1).default; } });
		var yo_js_1 = /*@__PURE__*/ requireYo();
		Object.defineProperty(exports, "yo", { enumerable: true, get: function () { return __importDefault(yo_js_1).default; } });

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (locales));
	return locales;
}

var registries = {};

var hasRequiredRegistries;

function requireRegistries () {
	if (hasRequiredRegistries) return registries;
	hasRequiredRegistries = 1;
	(function (exports) {
		var _a;
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.globalRegistry = exports.$ZodRegistry = exports.$input = exports.$output = void 0;
		exports.registry = registry;
		exports.$output = Symbol("ZodOutput");
		exports.$input = Symbol("ZodInput");
		class $ZodRegistry {
		    constructor() {
		        this._map = new WeakMap();
		        this._idmap = new Map();
		    }
		    add(schema, ..._meta) {
		        const meta = _meta[0];
		        this._map.set(schema, meta);
		        if (meta && typeof meta === "object" && "id" in meta) {
		            this._idmap.set(meta.id, schema);
		        }
		        return this;
		    }
		    clear() {
		        this._map = new WeakMap();
		        this._idmap = new Map();
		        return this;
		    }
		    remove(schema) {
		        const meta = this._map.get(schema);
		        if (meta && typeof meta === "object" && "id" in meta) {
		            this._idmap.delete(meta.id);
		        }
		        this._map.delete(schema);
		        return this;
		    }
		    get(schema) {
		        // return this._map.get(schema) as any;
		        // inherit metadata
		        const p = schema._zod.parent;
		        if (p) {
		            const pm = { ...(this.get(p) ?? {}) };
		            delete pm.id; // do not inherit id
		            const f = { ...pm, ...this._map.get(schema) };
		            return Object.keys(f).length ? f : undefined;
		        }
		        return this._map.get(schema);
		    }
		    has(schema) {
		        return this._map.has(schema);
		    }
		}
		exports.$ZodRegistry = $ZodRegistry;
		// registries
		function registry() {
		    return new $ZodRegistry();
		}
		(_a = globalThis).__zod_globalRegistry ?? (_a.__zod_globalRegistry = registry());
		exports.globalRegistry = globalThis.__zod_globalRegistry;

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (registries));
	return registries;
}

var compile$3 = {};

var hasRequiredCompile$3;

function requireCompile$3 () {
	if (hasRequiredCompile$3) return compile$3;
	hasRequiredCompile$3 = 1;
	(function (exports) {
		var __createBinding = (compile$3 && compile$3.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (compile$3 && compile$3.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (compile$3 && compile$3.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodCompileUnsupportedError = exports.ZodCompileAsyncError = exports.INVALID = void 0;
		exports.compile = compile;
		exports.withParser = withParser;
		exports.compileFn = compileFn;
		const core_js_1 = /*@__PURE__*/ requireCore$1();
		const doc_js_1 = /*@__PURE__*/ requireDoc();
		const memoizer_js_1 = /*@__PURE__*/ requireMemoizer();
		const regexes = __importStar(/*@__PURE__*/ requireRegexes());
		const schemas_js_1 = /*@__PURE__*/ requireSchemas$1();
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		/** @internal Sentinel the compiled fast path returns when validation fails. */
		exports.INVALID = Symbol.for("zod.compile.invalid");
		// Set on the parse ctx when a compiled wrapper falls back to the runtime, so nested compiled wrappers skip their fast paths for the rest of that parse.
		const FALLBACK_FLAG = Symbol.for("zod.compile.fallback");
		/** Raised when the schema contains async refinements or transforms. Surfaces only under `compile(schema, { strict: true })`. */
		class ZodCompileAsyncError extends Error {
		    constructor(message = "z.compile does not support async refinements, transforms, or checks") {
		        super(message);
		        this.name = "ZodCompileAsyncError";
		    }
		}
		exports.ZodCompileAsyncError = ZodCompileAsyncError;
		/**
		 * Raised when the schema contains a feature whose semantics the fast path
		 * can't fully model. Both the shim in `zod/compile` and the default
		 * `compile()` fall back to the runtime parser for that schema; only
		 * `compile(schema, { strict: true })` lets it surface.
		 */
		class ZodCompileUnsupportedError extends Error {
		    constructor(feature, islandable = true) {
		        super(`z.compile does not support ${feature}; this schema must use the runtime parser`);
		        this.name = "ZodCompileUnsupportedError";
		        this.islandable = islandable;
		    }
		}
		exports.ZodCompileUnsupportedError = ZodCompileUnsupportedError;
		/**
		 * Build the validator `validate` calls: the same codegen as the parser with the output construction
		 * dropped. A schema the flag cannot express reuses the parser, which still answers correctly — it
		 * just builds a value nothing reads.
		 */
		function compileValidator(schema, parser) {
		    try {
		        return compileFn(schema, { assertOnly: true });
		    }
		    catch {
		        return parser;
		    }
		}
		/**
		 * AOT-compile a Zod schema. Returns a clone whose `_zod.run` calls a generated
		 * fast path first and falls back to the original runtime parser on failure.
		 *
		 * - Forward direction only. Backward (encode), async, and `skipChecks` paths
		 *   bypass the fast path and use the runtime directly.
		 * - Never throws. A schema the fast path can't model is returned unchanged and
		 *   keeps using the runtime parser. Pass `{ strict: true }` to get the refusal
		 *   as a thrown `ZodCompileUnsupportedError` / `ZodCompileAsyncError` instead.
		 * - The original schema is unchanged. The clone shares children by reference.
		 */
		function compile(schema, options) {
		    try {
		        const parser = compileFn(schema);
		        const clone = withParser(schema, parser);
		        // withParser leaves the parser as its own validator; the generated assert-only variant is better when we have one
		        clone._zod.bag.validator = compileValidator(schema, parser);
		        return clone;
		    }
		    catch (err) {
		        if (options?.strict)
		            throw err;
		        // a schema we can't compile still has to work, so hand it back untouched on the runtime parser — the same silent fallback global mode already does
		        return schema;
		    }
		}
		/**
		 * Install an already-generated parser as a schema's fast path. Returns a clone; the original is
		 * unchanged.
		 *
		 * The parser takes the input and returns the parsed value, or `INVALID` to hand the parse to the
		 * runtime. It must be synchronous and forward-direction, and it must build fresh output rather than
		 * return its input — Zod cannot check either, and a wrong *success* is returned to the caller as-is.
		 *
		 * `compile()` is the ordinary entry point. This is for a build-time or native compiler that produces
		 * a parser where `new Function` is unavailable.
		 */
		function withParser(schema, parser) {
		    // generated code never receives the parse context, so only the runtime can close a reference cycle; compileFn refuses these too
		    if ((0, memoizer_js_1.isRecursiveSchema)(schema)) {
		        throw new ZodCompileUnsupportedError("a schema whose subtree contains a reference cycle");
		    }
		    const clone = util.clone(schema);
		    // Capture the source-of-truth runtime eagerly. If schema._zod.run is itself a shim installed by global-mode (`__originalRun` set), unwrap past it. Otherwise capturing the live property lazily would let a later self- replacement of schema._zod.run feed our wrapper back into itself.
		    const liveRun = schema._zod.run;
		    const originalRun = liveRun.__originalRun ?? liveRun;
		    // Delegate to the *original* schema's run on bypass/fallback, not the clone's. The original closed over its own `inst` at construction time, and issue payloads use that reference to derive things like the class name for `z.instanceof(Test)`; calling the clone's freshly-initialized run would push issues with `inst === clone` and diverge from the original schema's error messages.
		    const wrapped = (payload, ctx) => {
		        if (ctx?.async ||
		            ctx?.direction === "backward" ||
		            ctx?.skipChecks ||
		            ctx?.[FALLBACK_FLAG]) {
		            return originalRun(payload, ctx);
		        }
		        // A memoized back-edge: only the runtime can close a reference cycle, and a transform on one must raise $ZodCyclicError from its own parse.
		        if (ctx && (0, memoizer_js_1.isBackEdge)(ctx, payload.value)) {
		            return originalRun(payload, ctx);
		        }
		        const out = parser(payload.value);
		        if (out !== exports.INVALID) {
		            payload.value = out;
		            return payload;
		        }
		        // Mark this parse as runtime-driven: under global mode every nested schema carries its own compiled wrapper, and without the flag the parent's runtime fallback re-enters each child's fast path, running user callbacks a third time on invalid input.
		        if (ctx)
		            ctx[FALLBACK_FLAG] = true;
		        return originalRun(payload, ctx);
		    };
		    // Let later compiles of (or through) this run unwrap to the true runtime — both the global shim and repeated z.compile calls rely on this. The bag also carries the parser and the validator, so the standalone validate can skip the payload and wrapper on the happy path.
		    wrapped.__originalRun = originalRun;
		    clone._zod.bag.fallbackRun = originalRun;
		    // a supplied parser answers `validate` too: one implementation means parse and validate cannot disagree, and its undefined `definite` keeps the runtime re-parse on every rejection
		    clone._zod.bag.validator = parser;
		    clone._zod.run = wrapped;
		    // The fast parse/safeParse closures fall back through the source schema's methods. If the source is shim- or wrapper-managed, those methods route into a compiled run and would execute user callbacks a third time on invalid input — the plain method → wrapper path is exactly 2x, so skip.
		    if (!liveRun.__originalRun)
		        installCompiledUserMethods(clone, schema, parser);
		    return clone;
		}
		function installCompiledUserMethods(target, source, parser) {
		    const targetAny = target;
		    const sourceAny = source;
		    if (typeof sourceAny.safeParse === "function") {
		        const originalSafeParse = sourceAny.safeParse;
		        targetAny.safeParse = (data, params) => {
		            const out = parser(data);
		            if (out !== exports.INVALID) {
		                return { success: true, data: out };
		            }
		            return originalSafeParse(data, params);
		        };
		    }
		    if (typeof sourceAny.parse === "function") {
		        const originalParse = sourceAny.parse;
		        targetAny.parse = (data, params) => {
		            const out = parser(data);
		            if (out !== exports.INVALID) {
		                return out;
		            }
		            return originalParse(data, params);
		        };
		    }
		}
		/**
		 * @internal Generate the standalone compiled function: a parser by default, a validator under
		 * `assertOnly`. Returns the parsed value, `true` where nothing reads the output, or `INVALID`. Consumers use `compile()`.
		 */
		function compileFn(schema, options) {
		    // Cycle-breaking is keyed on the parse context, which generated code never receives. `shape` can be a getter that throws (z.pick() with an unrecognized mask key), so treat "can't tell" as recursive.
		    let recursive = true;
		    try {
		        recursive = (0, memoizer_js_1.isRecursiveSchema)(schema);
		    }
		    catch { }
		    if (recursive) {
		        throw new ZodCompileUnsupportedError("a schema whose subtree contains a reference cycle");
		    }
		    const ctx = {
		        constants: new Map(),
		        constantCounter: 0,
		        varCounter: 0,
		        definite: true,
		    };
		    const doc = new doc_js_1.Doc(["input"]);
		    const outputAccessor = generateCheck(doc, ctx, schema, "input", !options?.assertOnly);
		    // In assert mode a root that built nothing has already returned INVALID on every failure, so reaching the end means valid.
		    doc.write(outputAccessor === null ? `return true;` : `return ${outputAccessor};`);
		    // Build the function with hoisted constants Always include INVALID as the first constant
		    const constantNames = ["INVALID", ...ctx.constants.keys()];
		    const constantValues = [exports.INVALID, ...ctx.constants.values()];
		    const code = doc.content.join("\n");
		    const fullCode = options?.debug
		        ? constantNames.length > 0
		            ? `// Constants: ${constantNames.join(", ")}\n${code}`
		            : code
		        : "";
		    const F = Function;
		    const factoryCode = `return (input) => {\n${code}\n}`;
		    let fn;
		    try {
		        const factory = new F(...constantNames, factoryCode);
		        fn = factory(...constantValues);
		    }
		    catch (err) {
		        // Malformed generated code (or a CSP environment rejecting `new Function`) surfaces as a typed error so the global shim falls back to the runtime instead of crashing with a raw SyntaxError/EvalError.
		        throw new ZodCompileUnsupportedError(`this schema (generated code failed to evaluate: ${err.message})`);
		    }
		    if (options?.debug) {
		        fn.code = fullCode;
		    }
		    fn.definite = ctx.definite;
		    return fn;
		}
		function addConstant(ctx, value) {
		    // Check if we already have this constant
		    for (const [name, v] of ctx.constants) {
		        if (v === value)
		            return name;
		    }
		    const name = `c${ctx.constantCounter++}`;
		    ctx.constants.set(name, value);
		    return name;
		}
		/** Hoists a user-supplied callback. Anything the schema's author wrote can throw, and generated code can reject an earlier sibling before ever reaching it, so this clears `definite` — a rejection is then no longer proof that the interpreter would have rejected rather than thrown. */
		function addUserConstant(ctx, fn) {
		    ctx.definite = false;
		    return addConstant(ctx, fn);
		}
		function newVar(ctx) {
		    return `v${ctx.varCounter++}`;
		}
		// Runs a child schema as a black box: its value, or INVALID for a failure or an async run.
		function runtimeRun(schema, value) {
		    const result = schema._zod.run({ value, issues: [] }, {});
		    if (result && typeof result.then === "function")
		        return exports.INVALID;
		    const r = result;
		    return r.issues.length === 0 ? r.value : exports.INVALID;
		}
		// `null` means the node built no value because nothing reads it. The overloads keep that case out of the 20-odd callers that always want one, so only a caller passing needsValue has to handle it.
		function compileChild(doc, ctx, schema, accessor, needsValue = true) {
		    const contentLen = doc.content.length;
		    const constantCount = ctx.constants.size;
		    const constantCounter = ctx.constantCounter;
		    const varCounter = ctx.varCounter;
		    try {
		        return generateCheck(doc, ctx, schema, accessor, needsValue);
		    }
		    catch (err) {
		        if (!(err instanceof ZodCompileUnsupportedError) || !err.islandable)
		            throw err;
		        doc.content.length = contentLen;
		        if (ctx.constants.size > constantCount) {
		            const trailing = Array.from(ctx.constants.keys()).slice(constantCount);
		            for (const k of trailing)
		                ctx.constants.delete(k);
		        }
		        ctx.constantCounter = constantCounter;
		        ctx.varCounter = varCounter;
		        return emitRuntimeIsland(doc, ctx, schema, accessor);
		    }
		}
		function emitRuntimeIsland(doc, ctx, schema, accessor) {
		    // an islanded child answers INVALID for an async run too, which the interpreter throws for
		    ctx.definite = false;
		    const schemaConst = addConstant(ctx, schema);
		    const runConst = addConstant(ctx, runtimeRun);
		    const outVar = newVar(ctx);
		    doc.write(`const ${outVar} = ${runConst}(${schemaConst}, ${accessor});`);
		    doc.write(`if (${outVar} === INVALID) return INVALID;`);
		    return outVar;
		}
		// Check classes whose `when` is auto-defaulted at init (checks.ts `when ??=`).
		const WHEN_DEFAULTED_CHECKS = new Set([
		    "max_size",
		    "min_size",
		    "size_equals",
		    "max_length",
		    "min_length",
		    "length_equals",
		]);
		function generateChecks(doc, ctx, schema, accessor) {
		    const schemaChecks = schema._zod.def.checks;
		    if (!schemaChecks || schemaChecks.length === 0)
		        return accessor;
		    // Track current accessor - may change if overwrite checks are encountered
		    let currentAccessor = accessor;
		    for (const check of schemaChecks) {
		        const def = check._zod.def;
		        // A custom `when` skips a check the fast path always runs; the one auto-defaulted on size/length classes matches its bail-on-first-failure.
		        if (def.when && !WHEN_DEFAULTED_CHECKS.has(def.check)) {
		            throw new ZodCompileUnsupportedError(`check with a custom "when" condition`);
		        }
		        switch (def.check) {
		            case "greater_than":
		                generateGreaterThanCheck(doc, ctx, def, currentAccessor);
		                break;
		            case "less_than":
		                generateLessThanCheck(doc, ctx, def, currentAccessor);
		                break;
		            case "multiple_of":
		                generateMultipleOfCheck(doc, ctx, def, currentAccessor);
		                break;
		            case "number_format":
		                generateNumberFormatCheck(doc, def, currentAccessor);
		                break;
		            case "min_length": {
		                const min = numericOperand(def.minimum, "min_length");
		                const len = codePointLengthVar(doc, ctx, currentAccessor, `${currentAccessor}.length >= ${min} && ${currentAccessor}.length < ${def.minimum * 2}`);
		                doc.write(`if (${len} < ${min}) return INVALID;`);
		                break;
		            }
		            case "max_length": {
		                const max = numericOperand(def.maximum, "max_length");
		                const len = codePointLengthVar(doc, ctx, currentAccessor, `${currentAccessor}.length > ${max}`);
		                doc.write(`if (${len} > ${max}) return INVALID;`);
		                break;
		            }
		            case "length_equals": {
		                const exact = numericOperand(def.length, "length_equals");
		                const len = codePointLengthVar(doc, ctx, currentAccessor, `${currentAccessor}.length >= ${exact} && ${currentAccessor}.length <= ${def.length * 2}`);
		                doc.write(`if (${len} !== ${exact}) return INVALID;`);
		                break;
		            }
		            case "min_size":
		                doc.write(`if (${currentAccessor}.size < ${numericOperand(def.minimum, "min_size")}) return INVALID;`);
		                break;
		            case "max_size":
		                doc.write(`if (${currentAccessor}.size > ${numericOperand(def.maximum, "max_size")}) return INVALID;`);
		                break;
		            case "size_equals":
		                doc.write(`if (${currentAccessor}.size !== ${numericOperand(def.size, "size_equals")}) return INVALID;`);
		                break;
		            case "string_format":
		                currentAccessor = generateStringFormatCheck(doc, ctx, def, currentAccessor);
		                break;
		            case "custom":
		                currentAccessor = generateCustomRefineCheck(doc, ctx, check, currentAccessor);
		                break;
		            case "bigint_format":
		                generateBigIntFormatCheck(doc, def, currentAccessor);
		                break;
		            case "mime_type":
		                generateMimeTypeCheck(doc, ctx, def, currentAccessor);
		                break;
		            case "property":
		                generatePropertyCheck(doc, ctx, def, currentAccessor);
		                break;
		            case "properties":
		                generatePropertiesChecks(doc, ctx, def, currentAccessor);
		                break;
		            case "overwrite": {
		                // Overwrite transforms the value - create new variable for transformed result
		                const newAccessor = newVar(ctx);
		                generateOverwriteCheck(doc, ctx, check, currentAccessor, newAccessor);
		                currentAccessor = newAccessor;
		                break;
		            }
		            default: {
		                throw new ZodCompileUnsupportedError(`check type ${def.check}`);
		            }
		        }
		    }
		    return currentAccessor;
		}
		// Emit the length operand for a length check, mirroring `$ZodCheckMinLength` and friends: strings measure in code points, everything else in `.length`. `inDoubt` is the caller's cheap UTF-16 bound test — outside it the unit count already settles the comparison, so the scan is skipped.
		function codePointLengthVar(doc, ctx, accessor, inDoubt) {
		    const cpLen = addConstant(ctx, util.codePointLength);
		    const v = newVar(ctx);
		    doc.write(`const ${v} = typeof ${accessor} === "string" && ${inDoubt} ? ${cpLen}(${accessor}) : ${accessor}.length;`);
		    return v;
		}
		// Emit a source operand for a gt/lt bound. Numbers inline; Dates hoist as a constant (relational operators compare via valueOf). NaN and Invalid Date bounds can't compile to a comparison that matches runtime semantics.
		/**
		 * A count bound reaches generated source verbatim, so a non-number would be
		 * emitted as code rather than as a value — `min('0) {} evil(); if (0')` writes an
		 * arbitrary statement into the function body. TypeScript types these as `number`
		 * and fromJSONSchema guards them, so this is a backstop rather than a live hole,
		 * but generated source is the one place a wrong type stops being a type error.
		 */
		function numericOperand(value, label) {
		    if (typeof value !== "number" || !Number.isFinite(value)) {
		        throw new ZodCompileUnsupportedError(`${label} bound of type ${typeof value}`);
		    }
		    return `${value}`;
		}
		function comparisonOperand(ctx, value) {
		    if (typeof value === "bigint")
		        return `${value}n`;
		    if (typeof value === "number") {
		        if (Number.isNaN(value))
		            throw new ZodCompileUnsupportedError("comparison check with NaN bound");
		        return `${value}`;
		    }
		    if (value instanceof Date) {
		        if (Number.isNaN(value.getTime())) {
		            throw new ZodCompileUnsupportedError("comparison check with Invalid Date bound");
		        }
		        return addConstant(ctx, value);
		    }
		    throw new ZodCompileUnsupportedError(`comparison check bound of type ${typeof value}`);
		}
		function generateGreaterThanCheck(doc, ctx, def, accessor) {
		    const op = def.inclusive ? "<" : "<=";
		    doc.write(`if (${accessor} ${op} ${comparisonOperand(ctx, def.value)}) return INVALID;`);
		}
		function generateLessThanCheck(doc, ctx, def, accessor) {
		    const op = def.inclusive ? ">" : ">=";
		    doc.write(`if (${accessor} ${op} ${comparisonOperand(ctx, def.value)}) return INVALID;`);
		}
		function generateMultipleOfCheck(doc, ctx, def, accessor) {
		    if (typeof def.value === "bigint") {
		        // a zero divisor has no compiled form: `x % 0n` throws
		        if (def.value === BigInt(0))
		            throw new ZodCompileUnsupportedError("multiple_of check with a zero divisor");
		        doc.write(`if (${accessor} % ${def.value}n !== 0n) return INVALID;`);
		    }
		    else {
		        // Float `%` has well-known precision issues for sub-integer steps
		        // (`1.5 % 0.1`, `2.5e-7 % 1e-7`). Defer to util.floatSafeRemainder so the
		        // exact tolerance logic stays in one place — single function call is fine
		        // since `multipleOf` runs at most once per number.
		        const remainder = addConstant(ctx, util.floatSafeRemainder);
		        doc.write(`if (${remainder}(${accessor}, ${numericOperand(def.value, "multiple_of")}) !== 0) return INVALID;`);
		    }
		}
		function generateNumberFormatCheck(doc, def, accessor) {
		    const format = def.format;
		    switch (format) {
		        case "safeint":
		            doc.write(`if (!Number.isSafeInteger(${accessor})) return INVALID;`);
		            break;
		        case "int32":
		            doc.write(`if (!Number.isInteger(${accessor}) || ${accessor} < -2147483648 || ${accessor} > 2147483647) return INVALID;`);
		            break;
		        case "uint32":
		            doc.write(`if (!Number.isInteger(${accessor}) || ${accessor} < 0 || ${accessor} > 4294967295) return INVALID;`);
		            break;
		        case "float32":
		            // Float32 range per util.NUMBER_FORMAT_RANGES
		            doc.write(`if (!Number.isFinite(${accessor}) || ${accessor} < -3.4028234663852886e38 || ${accessor} > 3.4028234663852886e38) return INVALID;`);
		            break;
		        case "float64":
		            doc.write(`if (!Number.isFinite(${accessor})) return INVALID;`);
		            break;
		        default: {
		            throw new ZodCompileUnsupportedError(`number format ${format}`);
		        }
		    }
		}
		function generateBigIntFormatCheck(doc, def, accessor) {
		    const format = def.format;
		    if (!format)
		        return; // undefined format means no range check
		    switch (format) {
		        case "int64":
		            doc.write(`if (${accessor} < -9223372036854775808n || ${accessor} > 9223372036854775807n) return INVALID;`);
		            break;
		        case "uint64":
		            doc.write(`if (${accessor} < 0n || ${accessor} > 18446744073709551615n) return INVALID;`);
		            break;
		        default: {
		            throw new ZodCompileUnsupportedError(`bigint format ${format}`);
		        }
		    }
		}
		function generateMimeTypeCheck(doc, ctx, def, accessor) {
		    const mimeTypes = def.mime;
		    if (mimeTypes && mimeTypes.length > 0) {
		        const mimeSet = addConstant(ctx, new Set(mimeTypes));
		        doc.write(`if (!${mimeSet}.has(${accessor}.type)) return INVALID;`);
		    }
		}
		// asserts each named property in place; children compile assert-only because z.properties never rebuilds its input
		function generatePropertiesChecks(doc, ctx, def, accessor) {
		    // a custom `when` gates the assertion at runtime; inside a union a wrongly-run branch is absorbed as a branch failure rather than falling back, so refuse at codegen
		    if (def.when) {
		        throw new ZodCompileUnsupportedError(`check with a custom "when" condition`);
		    }
		    // matches the runtime gate: the base schema already typed the value, so only a nullish one is rejected
		    doc.write(`if (${accessor} == null) return INVALID;`);
		    const shape = def.shape;
		    for (const key of Reflect.ownKeys(shape)) {
		        // a symbol has no source literal, so it is hoisted as a constant
		        const keyExpr = typeof key === "symbol" ? addConstant(ctx, key) : util.esc(key);
		        // cache the property read so a getter runs exactly once, matching the runtime
		        const inputVar = newVar(ctx);
		        doc.write(`const ${inputVar} = ${accessor}[${keyExpr}];`);
		        compileChild(doc, ctx, shape[key], inputVar, false);
		    }
		}
		function generatePropertyCheck(doc, ctx, def, accessor) {
		    const propAccessor = `${accessor}[${JSON.stringify(def.property)}]`;
		    generateCheck(doc, ctx, def.schema, propAccessor);
		}
		function generateOverwriteCheck(doc, ctx, check, currentAccessor, newAccessor) {
		    const tx = check._zod.def.tx;
		    if (!tx) {
		        throw new ZodCompileUnsupportedError("overwrite check without a transform function");
		    }
		    // Check for async transform
		    if (isAsyncFunction(tx)) {
		        throw new ZodCompileAsyncError("z.compile: async overwrite transforms are not supported");
		    }
		    // Hoist the transform function as a constant and apply it
		    const txConst = addConstant(ctx, tx);
		    doc.write(`const ${newAccessor} = ${txConst}(${currentAccessor});`);
		}
		/** A predicate that hands back a thenable is an async check reached synchronously, and the interpreter throws `$ZodAsyncError` for it. Returning INVALID instead would be a bail-out, and a union reads a bail-out as a rejected branch and answers with a later one — so the throw has to survive into generated code. */
		function throwAsync() {
		    throw new core_js_1.$ZodAsyncError();
		}
		/** Shared `addIssue` for the spoofed payloads a refine, check or transform receives. Allocating one per call — a fresh closure plus a `this`-bound method on a fresh literal — pinned every payload-allocating schema at ~2.7M ops/sec against 135M for a plain object literal. It captures nothing per call; it only reaches `this.issues`. */
		function pushIssue(issue) {
		    this.issues.push(issue);
		}
		function generateCustomRefineCheck(doc, ctx, check, accessor) {
		    const def = check._zod.def;
		    if (def.fn) {
		        // Simple predicate function (from .refine())
		        if (isAsyncFunction(def.fn)) {
		            throw new ZodCompileAsyncError("z.compile: async .refine() predicates are not supported");
		        }
		        const fnConst = addUserConstant(ctx, def.fn);
		        const throwAsyncConst = addConstant(ctx, throwAsync);
		        const resVar = newVar(ctx);
		        doc.write(`const ${resVar} = ${fnConst}(${accessor});`);
		        // A thenable is truthy, so it would otherwise read as a pass. It is not a rejection either: the interpreter throws, and INVALID inside a union would just hand the parse to the next branch.
		        doc.write(`if (${resVar} instanceof Promise) ${throwAsyncConst}();`);
		        doc.write(`if (!${resVar}) return INVALID;`);
		        // A `.refine()` predicate only answers yes or no; it cannot rewrite the value.
		        return accessor;
		    }
		    if (check._zod.check) {
		        if (isAsyncFunction(check._zod.check)) {
		            throw new ZodCompileAsyncError("z.compile: async .superRefine() / check functions are not supported");
		        }
		        // SuperRefine or other check function - need to spoof context Create a helper that runs the check and returns true if no issues
		        const checkFn = check._zod.check;
		        // `$RefinementCtx` extends the parse payload, so a check may rewrite
		        // `ctx.value` as well as push issues — `.superRefine((v, ctx) => { ctx.value =
		        // v.trim() })` is a value transform. Returning only a boolean discarded that
		        // write and emitted the untrimmed input. Hand the value back and thread it on.
		        const helperFn = (value) => {
		            const fakePayload = { value, issues: [], addIssue: pushIssue };
		            const result = checkFn(fakePayload);
		            // Throw rather than return INVALID: the interpreter throws here, and a union would read INVALID as a rejected branch.
		            if (result instanceof Promise)
		                throwAsync();
		            return fakePayload.issues.length === 0 ? fakePayload.value : exports.INVALID;
		        };
		        const helperConst = addUserConstant(ctx, helperFn);
		        const outVar = newVar(ctx);
		        doc.write(`const ${outVar} = ${helperConst}(${accessor});`);
		        doc.write(`if (${outVar} === INVALID) return INVALID;`);
		        return outVar;
		    }
		    throw new ZodCompileUnsupportedError("custom check without a predicate or check function");
		}
		/**
		 * Built-in formats that validate with nothing but `def.pattern`, so compiling
		 * the regex reproduces the runtime exactly. Deliberately an allowlist: a format
		 * missing from it loses its fast path, while a format wrongly added to it
		 * silently accepts input the runtime rejects. Formats that layer extra
		 * validation over a shape-only pattern (`credit_card`, `base64`, `ipv6`, …) are
		 * handled above by hoisting the runtime validator itself.
		 */
		const PATTERN_IS_COMPLETE = new Set([
		    "cidrv4",
		    "cuid",
		    "cuid2",
		    "date",
		    "datetime",
		    "duration",
		    "e164",
		    "email",
		    "emoji",
		    "ends_with",
		    "guid",
		    "includes",
		    "ipv4",
		    "ksuid",
		    "lowercase",
		    "mac",
		    "nanoid",
		    "regex",
		    "starts_with",
		    "time",
		    "ulid",
		    "uppercase",
		    "uuid",
		    "xid",
		]);
		function generateStringFormatCheck(doc, ctx, def, accessor, needsValue = true) {
		    // Some string formats do runtime validation beyond their advertised pattern. For cheap pure utility checks, hoist the runtime function and call it so the fast path stays correct without cloning the utility logic into codegen.
		    const fmt = def.format;
		    if (fmt === "base64") {
		        const validator = addConstant(ctx, schemas_js_1.isValidBase64);
		        doc.write(`if (!${validator}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    if (fmt === "base64url") {
		        const validator = addConstant(ctx, schemas_js_1.isValidBase64URL);
		        doc.write(`if (!${validator}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    if (fmt === "jwt") {
		        const validator = addConstant(ctx, schemas_js_1.isValidJWT);
		        const alg = addConstant(ctx, def.alg ?? null);
		        doc.write(`if (!${validator}(${accessor}, ${alg})) return INVALID;`);
		        return accessor;
		    }
		    if (fmt === "ipv6") {
		        const validator = addConstant(ctx, schemas_js_1.isValidIPv6);
		        doc.write(`if (!${validator}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    if (fmt === "cidrv6") {
		        const validator = addConstant(ctx, schemas_js_1.isValidCIDRv6);
		        doc.write(`if (!${validator}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    if (fmt === "credit_card") {
		        const validator = addConstant(ctx, schemas_js_1.isValidCreditCard);
		        doc.write(`if (!${validator}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    if (fmt === "iban") {
		        const validator = addConstant(ctx, schemas_js_1.isValidIBAN);
		        doc.write(`if (!${validator}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    const formatDef = def;
		    if (fmt === "url" ||
		        fmt === "httpurl" ||
		        formatDef.normalize ||
		        formatDef.hostname !== undefined ||
		        formatDef.protocol !== undefined) {
		        // Same three predicates the runtime calls, in the same order, so there is no second URL implementation to drift. Which options exist is known now, so the calls the runtime makes conditionally are emitted conditionally instead.
		        const parseConst = addConstant(ctx, schemas_js_1.validateURL);
		        const defConst = addConstant(ctx, def);
		        const trimVar = newVar(ctx);
		        const urlVar = newVar(ctx);
		        doc.write(`const ${trimVar} = ${accessor}.trim();`);
		        doc.write(`const ${urlVar} = ${parseConst}(${trimVar}, ${defConst});`);
		        doc.write(`if (typeof ${urlVar} === "number") return INVALID;`);
		        if (formatDef.hostname !== undefined) {
		            const hostnameConst = addConstant(ctx, schemas_js_1.urlHostnameOk);
		            doc.write(`if (!${hostnameConst}(${urlVar}, ${defConst}.hostname)) return INVALID;`);
		        }
		        if (formatDef.protocol !== undefined) {
		            const protocolConst = addConstant(ctx, schemas_js_1.urlProtocolOk);
		            doc.write(`if (!${protocolConst}(${urlVar}, ${defConst}.protocol)) return INVALID;`);
		        }
		        if (!needsValue)
		            return null;
		        const outputVar = newVar(ctx);
		        const outputExpr = formatDef.normalize ? `${urlVar}.href` : `${addConstant(ctx, schemas_js_1.stripTabAndNewline)}(${trimVar})`;
		        doc.write(`const ${outputVar} = ${outputExpr};`);
		        return outputVar;
		    }
		    // A custom string format carries the predicate the runtime actually calls. Hoist and call it instead of testing `def.pattern`: the two only coincide when the format was built from a RegExp, and relying on that coupling is how supplemental validation gets dropped.
		    const customFn = def.fn;
		    if (customFn) {
		        if (isAsyncFunction(customFn))
		            throw new ZodCompileUnsupportedError(`async string format ${fmt}`);
		        const fnConst = addConstant(ctx, customFn);
		        doc.write(`if (!${fnConst}(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    // Formats whose `pattern` IS the whole check. An allowlist rather than `if (def.pattern)`, because credit_card, base64 and ipv6 carry a shape-only pattern and validate the rest separately.
		    if (PATTERN_IS_COMPLETE.has(fmt) && def.pattern) {
		        const patternConst = addConstant(ctx, def.pattern);
		        doc.write(`${patternConst}.lastIndex = 0;`);
		        doc.write(`if (!${patternConst}.test(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    const format = def.format;
		    switch (format) {
		        case "regex":
		            // A regex check with a pattern returned above. Reaching here means there is no pattern to test, and accepting unconditionally would pass every input, so hand the schema back to the runtime.
		            throw new ZodCompileUnsupportedError("regex format without a pattern");
		        case "lowercase":
		            doc.write(`if (${accessor} !== ${accessor}.toLowerCase()) return INVALID;`);
		            break;
		        case "uppercase":
		            doc.write(`if (${accessor} !== ${accessor}.toUpperCase()) return INVALID;`);
		            break;
		        case "includes":
		            doc.write(`if (!${accessor}.includes(${util.esc(def.includes)})) return INVALID;`);
		            break;
		        case "starts_with": {
		            const prefix = def.prefix;
		            doc.write(`if (${accessor}.slice(0, ${prefix.length}) !== ${util.esc(prefix)}) return INVALID;`);
		            break;
		        }
		        case "ends_with": {
		            const suffix = def.suffix;
		            doc.write(`if (${accessor}.slice(-${suffix.length}) !== ${util.esc(suffix)}) return INVALID;`);
		            break;
		        }
		        default: {
		            throw new ZodCompileUnsupportedError(`string format ${format}`);
		        }
		    }
		    return accessor;
		}
		function generateCheck(doc, ctx, schema, accessor, needsValue = true) {
		    const def = schema._zod.def;
		    const type = def.type;
		    // A coercing schema would compile to the bare type test and reject what it should convert; inside a union that reads as a rejected branch, so refuse at codegen.
		    if (def.coerce) {
		        throw new ZodCompileUnsupportedError(`coercion (z.coerce.${type}())`);
		    }
		    // A node builds its output when its caller reads one, or when it carries checks of its own, since a check reads what was built. One polarity for the whole walk: this is what the node does, and it is what its children are told they need.
		    const buildsValue = needsValue || !!def.checks?.length;
		    let typeAccessor;
		    switch (type) {
		        case "string":
		            typeAccessor = generateStringCheck(doc, ctx, schema, accessor, buildsValue);
		            break;
		        case "number":
		            typeAccessor = generateNumberCheck(doc, schema, accessor);
		            break;
		        case "boolean":
		            typeAccessor = generateBooleanCheck(doc, accessor);
		            break;
		        case "bigint":
		            typeAccessor = generateBigIntCheck(doc, schema, accessor);
		            break;
		        case "symbol":
		            typeAccessor = generateSymbolCheck(doc, accessor);
		            break;
		        case "undefined":
		            typeAccessor = generateUndefinedCheck(doc, accessor);
		            break;
		        case "null":
		            typeAccessor = generateNullCheck(doc, accessor);
		            break;
		        case "any":
		        case "unknown":
		            // No check needed - pass through
		            typeAccessor = accessor;
		            break;
		        case "never":
		            doc.write("return INVALID;");
		            typeAccessor = accessor;
		            break;
		        case "void":
		            typeAccessor = generateVoidCheck(doc, accessor);
		            break;
		        case "nan":
		            typeAccessor = generateNaNCheck(doc, accessor);
		            break;
		        case "date":
		            typeAccessor = generateDateCheck(doc, accessor);
		            break;
		        case "object":
		            typeAccessor = generateObjectCheck(doc, ctx, schema, accessor, buildsValue);
		            break;
		        case "optional":
		            typeAccessor = generateOptionalCheck(doc, ctx, schema, accessor, buildsValue);
		            break;
		        case "nullable":
		            typeAccessor = generateNullableCheck(doc, ctx, schema, accessor, buildsValue);
		            break;
		        case "array":
		            typeAccessor = generateArrayCheck(doc, ctx, schema, accessor, buildsValue);
		            break;
		        case "literal":
		            typeAccessor = generateLiteralCheck(doc, ctx, schema, accessor);
		            break;
		        case "enum":
		            typeAccessor = generateEnumCheck(doc, ctx, schema, accessor);
		            break;
		        case "readonly": {
		            const innerOut = generateWrapperCheck(doc, ctx, schema, accessor);
		            // Runtime freezes the parsed value (schemas.ts handleReadonlyResult).
		            const frozenVar = newVar(ctx);
		            doc.write(`const ${frozenVar} = Object.freeze(${innerOut});`);
		            typeAccessor = frozenVar;
		            break;
		        }
		        case "success":
		            // Runtime output is `issues.length === 0`. The fast path only reaches
		            // here when the inner check passed (failure returns INVALID and the
		            // runtime fallback reproduces the inner issues), so the output is
		            // always `true`.
		            generateWrapperCheck(doc, ctx, schema, accessor);
		            typeAccessor = "true";
		            break;
		        case "default":
		        case "prefault":
		            typeAccessor = generateDefaultCheck(doc, ctx, schema, accessor);
		            break;
		        case "nonoptional":
		            typeAccessor = generateNonOptionalCheck(doc, ctx, schema, accessor);
		            break;
		        case "tuple":
		            typeAccessor = generateTupleCheck(doc, ctx, schema, accessor);
		            break;
		        case "union":
		            typeAccessor = generateUnionCheck(doc, ctx, schema, accessor);
		            break;
		        case "intersection":
		            typeAccessor = generateIntersectionCheck(doc, ctx, schema, accessor);
		            break;
		        case "record":
		            typeAccessor = generateRecordCheck(doc, ctx, schema, accessor);
		            break;
		        case "map":
		            typeAccessor = generateMapCheck(doc, ctx, schema, accessor);
		            break;
		        case "set":
		            typeAccessor = generateSetCheck(doc, ctx, schema, accessor);
		            break;
		        case "file":
		            typeAccessor = generateFileCheck(doc, accessor);
		            break;
		        case "template_literal":
		            typeAccessor = generateTemplateLiteralCheck(doc, ctx, schema, accessor);
		            break;
		        case "lazy":
		            typeAccessor = generateLazyCheck(doc, ctx, schema, accessor);
		            break;
		        case "pipe":
		            typeAccessor = generatePipeCheck(doc, ctx, schema, accessor);
		            break;
		        case "custom":
		            typeAccessor = generateCustomCheck(doc, ctx, schema, accessor);
		            break;
		        case "transform":
		            typeAccessor = generateTransformCheck(doc, ctx, schema, accessor);
		            break;
		        case "catch":
		            typeAccessor = generateCatchCheck(doc, ctx, schema, accessor);
		            break;
		        default: {
		            throw new ZodCompileUnsupportedError(`schema type ${type}`);
		        }
		    }
		    // a node that built nothing has no checks to run: not building requires an empty check list
		    if (typeAccessor === null)
		        return null;
		    // Generate checks after the type-specific validation (may transform value)
		    return generateChecks(doc, ctx, schema, typeAccessor);
		}
		function generateStringCheck(doc, ctx, schema, accessor, needsValue = true) {
		    doc.write(`if (typeof ${accessor} !== "string") return INVALID;`);
		    // z.email() carries its format on the def, z.string().email() in def.checks; both route here so the format table has no second copy to drift from.
		    const def = schema._zod.def;
		    if (def.format === undefined)
		        return accessor;
		    return generateStringFormatCheck(doc, ctx, def, accessor, needsValue);
		}
		function generateNumberCheck(doc, schema, accessor) {
		    // Runtime z.number() rejects NaN and ±Infinity. Number.isFinite covers both.
		    doc.write(`if (typeof ${accessor} !== "number" || !Number.isFinite(${accessor})) return INVALID;`);
		    // Mini factories like z.int(), z.int32(), z.uint32(), z.float32() bake a number_format check into the schema def itself (not into def.checks). Apply the same constraint here.
		    const def = schema._zod.def;
		    if (def.check === "number_format" && def.format) {
		        generateNumberFormatCheck(doc, { format: def.format }, accessor);
		    }
		    return accessor;
		}
		function generateBooleanCheck(doc, accessor) {
		    doc.write(`if (typeof ${accessor} !== "boolean") return INVALID;`);
		    return accessor;
		}
		function generateBigIntCheck(doc, schema, accessor) {
		    doc.write(`if (typeof ${accessor} !== "bigint") return INVALID;`);
		    // Handle bigint format (int64, uint64) directly on the schema def
		    const def = schema._zod.def;
		    if (def.format) {
		        switch (def.format) {
		            case "int64":
		                doc.write(`if (${accessor} < -9223372036854775808n || ${accessor} > 9223372036854775807n) return INVALID;`);
		                break;
		            case "uint64":
		                doc.write(`if (${accessor} < 0n || ${accessor} > 18446744073709551615n) return INVALID;`);
		                break;
		        }
		    }
		    return accessor;
		}
		function generateSymbolCheck(doc, accessor) {
		    doc.write(`if (typeof ${accessor} !== "symbol") return INVALID;`);
		    return accessor;
		}
		function generateUndefinedCheck(doc, accessor) {
		    doc.write(`if (${accessor} !== undefined) return INVALID;`);
		    return accessor;
		}
		function generateNullCheck(doc, accessor) {
		    doc.write(`if (${accessor} !== null) return INVALID;`);
		    return accessor;
		}
		function generateVoidCheck(doc, accessor) {
		    doc.write(`if (${accessor} !== undefined) return INVALID;`);
		    return accessor;
		}
		function generateNaNCheck(doc, accessor) {
		    doc.write(`if (typeof ${accessor} !== "number" || !Number.isNaN(${accessor})) return INVALID;`);
		    return accessor;
		}
		function generateDateCheck(doc, accessor) {
		    doc.write(`if (!(${accessor} instanceof Date) || Number.isNaN(${accessor}.getTime())) return INVALID;`);
		    return accessor;
		}
		function generateObjectCheck(doc, ctx, schema, accessor, buildsValue = true) {
		    const def = schema._zod.def;
		    // Check that input is a non-null, non-array object
		    doc.write(`if (typeof ${accessor} !== "object" || ${accessor} === null || Array.isArray(${accessor})) return INVALID;`);
		    const shape = def.shape;
		    const keys = Object.keys(shape);
		    const symbolKeys = Object.getOwnPropertySymbols(shape);
		    // a symbol has no source literal, so it is hoisted as a constant; `keys` stays string-only where the emitted code uses `for...in`
		    const allKeys = symbolKeys.length ? [...keys, ...symbolKeys] : keys;
		    const keyExpr = (k) => (typeof k === "symbol" ? addConstant(ctx, k) : util.esc(k));
		    const propKey = (k) => (typeof k === "symbol" ? `[${keyExpr(k)}]` : util.esc(k));
		    const propShape = shape;
		    // `__proto__` as an own shape key can't be expressed in an output object literal (the literal form sets the prototype instead of an own property).
		    if (keys.includes("__proto__")) {
		        throw new ZodCompileUnsupportedError('object shape key "__proto__"');
		    }
		    // Map from key to output accessor for that property
		    const propOutputs = new Map();
		    // Validate each property and collect output accessors
		    for (const key of allKeys) {
		        const propSchema = propShape[key];
		        const kx = keyExpr(key);
		        // Always cache the property read: the runtime reads input[key] exactly once, so a getter must not be re-read by checks or output assembly.
		        const inputVar = newVar(ctx);
		        doc.write(`const ${inputVar} = ${accessor}[${kx}];`);
		        if (propSchema._zod.optin !== undefined) {
		            // Any optin rung means the key may be omitted. The runtime runs the property anyway and ignores issues only when the key is genuinely absent, which is what makes exactOptional compositional.
		            const outputVar = newVar(ctx);
		            doc.write(`let ${outputVar} = (() => {`);
		            doc.indented((d) => {
		                const outputAccessor = compileChild(d, ctx, propSchema, inputVar);
		                d.write(`return ${outputAccessor};`);
		            });
		            doc.write(`})();`);
		            if (propSchema._zod.optout === "optional") {
		                doc.write(`if (${outputVar} === INVALID) {`);
		                doc.indented((d) => {
		                    d.write(`if (${kx} in ${accessor}) return INVALID;`);
		                    d.write(`${outputVar} = undefined;`);
		                });
		                doc.write(`}`);
		            }
		            else {
		                doc.write(`if (${outputVar} === INVALID) return INVALID;`);
		            }
		            propOutputs.set(key, outputVar);
		        }
		        else {
		            if (requiresPresenceCheck(propSchema)) {
		                doc.write(`if (!(${kx} in ${accessor})) return INVALID;`);
		            }
		            // Generate check and get output accessor
		            const outputAccessor = compileChild(doc, ctx, propSchema, inputVar, buildsValue);
		            if (outputAccessor !== null)
		                propOutputs.set(key, outputAccessor);
		        }
		    }
		    // Handle catchall
		    const catchall = def.catchall;
		    let unknownKeysMode = "none";
		    if (catchall) {
		        const catchallType = catchall._zod.def.type;
		        if (catchallType === "never") {
		            // Strict: one `for...in`, as the runtime does, so inherited enumerable keys count. An undeclared `__proto__` is reported here and excluded only from the output (#6221); an own-key count would wrongly reject a class instance.
		            const condition = keys.map((k) => `k !== ${util.esc(k)}`).join(" && ") || "true";
		            doc.write(`for (const k in ${accessor}) {`);
		            doc.indented((d) => {
		                d.write(`if (${condition}) return INVALID;`);
		            });
		            doc.write(`}`);
		        }
		        else if ((catchallType === "unknown" || catchallType === "any") && !catchall._zod.def.checks?.length) {
		            unknownKeysMode = "passthrough";
		        }
		        else {
		            unknownKeysMode = "schema";
		        }
		    }
		    // else: strip mode (no catchall) - unknown keys ignored, only include known keys
		    // defaulted required outputs keep their keys even when undefined
		    const outputVar = newVar(ctx);
		    const hasConditionalKeys = allKeys.some((k) => mayOmitUndefined(propShape[k]) || dropsWhenAbsent(propShape[k]));
		    // Assert mode: every declared key is validated above, so the output literal and the unknown-key copy are pure waste. A `never` catchall already emitted its rejection loop; a schema catchall still has to validate the values it would otherwise have stored.
		    if (!buildsValue) {
		        if (unknownKeysMode === "schema") {
		            const knownSet = keys.length > 0 ? addConstant(ctx, new Set(keys)) : null;
		            doc.write(`for (const k in ${accessor}) {`);
		            doc.indented((d) => {
		                d.write(`if (k === "__proto__") continue;`);
		                if (knownSet)
		                    d.write(`if (${knownSet}.has(k)) continue;`);
		                const valVar = newVar(ctx);
		                d.write(`const ${valVar} = ${accessor}[k];`);
		                compileChild(d, ctx, catchall, valVar, false);
		            });
		            doc.write(`}`);
		        }
		        return null;
		    }
		    if (!hasConditionalKeys) {
		        const propLiterals = allKeys.map((k) => `${propKey(k)}: ${propOutputs.get(k)}`).join(", ");
		        doc.write(`const ${outputVar} = { ${propLiterals} };`);
		    }
		    else {
		        doc.write(`const ${outputVar} = {};`);
		        for (const k of allKeys) {
		            const kx = keyExpr(k);
		            const out = propOutputs.get(k);
		            if (dropsWhenAbsent(propShape[k])) {
		                doc.write(`if (${kx} in ${accessor}) ${outputVar}[${kx}] = ${out};`);
		            }
		            else if (mayOmitUndefined(propShape[k])) {
		                doc.write(`if (${out} !== undefined || ${kx} in ${accessor}) ${outputVar}[${kx}] = ${out};`);
		            }
		            else {
		                doc.write(`${outputVar}[${kx}] = ${out};`);
		            }
		        }
		    }
		    if (unknownKeysMode !== "none") {
		        // Unknown keys are written directly into the output after shape keys — for...in (like the runtime) so inherited enumerables participate.
		        const knownSet = keys.length > 0 ? addConstant(ctx, new Set(keys)) : null;
		        doc.write(`for (const k in ${accessor}) {`);
		        doc.indented((d) => {
		            // Skip __proto__: assigning obj["__proto__"] on a plain {} replaces the prototype via the setter rather than adding an own property. Mirrors the runtime catchall fix (#5898).
		            d.write(`if (k === "__proto__") continue;`);
		            if (knownSet)
		                d.write(`if (${knownSet}.has(k)) continue;`);
		            if (unknownKeysMode === "passthrough") {
		                d.write(`${outputVar}[k] = ${accessor}[k];`);
		            }
		            else {
		                const valVar = newVar(ctx);
		                d.write(`const ${valVar} = ${accessor}[k];`);
		                const catchallOut = compileChild(d, ctx, catchall, valVar);
		                d.write(`${outputVar}[k] = ${catchallOut};`);
		            }
		        });
		        doc.write(`}`);
		    }
		    return outputVar;
		}
		function generateOptionalCheck(doc, ctx, schema, accessor, buildsValue = true) {
		    const def = schema._zod.def;
		    if (isExactOptional(schema)) {
		        return generateCheck(doc, ctx, def.innerType, accessor, buildsValue);
		    }
		    // Same question $ZodOptional asks: only the top rung of the optin ladder substitutes a value for an absent input, so only it is worth running on `undefined`. Every other rung leaves the value intact, which is the skip branch below.
		    if (def.innerType._zod.optin === "defaulted") {
		        const outputVar = newVar(ctx);
		        const branchVar = newVar(ctx);
		        doc.write(`let ${outputVar};`);
		        doc.write(`if (${accessor} === undefined) {`);
		        doc.indented((d) => {
		            d.write(`const ${branchVar} = (() => {`);
		            d.indented((d2) => {
		                const innerOutput = generateCheck(d2, ctx, def.innerType, accessor);
		                d2.write(`return ${innerOutput};`);
		            });
		            d.write(`})();`);
		            d.write(`if (${branchVar} !== INVALID) ${outputVar} = ${branchVar};`);
		        });
		        doc.write(`} else {`);
		        doc.indented((d) => {
		            const innerOutput = generateCheck(d, ctx, def.innerType, accessor);
		            d.write(`${outputVar} = ${innerOutput};`);
		        });
		        doc.write(`}`);
		        return outputVar;
		    }
		    const outputVar = buildsValue ? newVar(ctx) : null;
		    if (outputVar)
		        doc.write(`let ${outputVar};`);
		    doc.write(`if (${accessor} !== undefined) {`);
		    doc.indented((d) => {
		        const innerOutput = generateCheck(d, ctx, def.innerType, accessor, buildsValue);
		        if (outputVar && innerOutput !== null)
		            d.write(`${outputVar} = ${innerOutput};`);
		    });
		    doc.write(`}`);
		    return outputVar;
		}
		function isExactOptional(schema) {
		    return schema._zod.traits?.has("$ZodExactOptional") === true;
		}
		// A value-level fast path reads an absent key as `undefined`, so z.undefined(), z.any() and unions containing undefined would accept a missing property the runtime rejects.
		function requiresPresenceCheck(schema) {
		    return schema._zod.optin === undefined && fastPathAcceptsAbsence(schema);
		}
		function fastPathAcceptsAbsence(schema) {
		    // An island is handed `input[key]` and cannot tell absent from explicitly undefined, so report absence-accepting and let the object emit the presence guard (#6405).
		    if (schema._zod.def.coerce)
		        return true;
		    const def = schema._zod.def;
		    switch (def.type) {
		        case "any":
		        case "unknown":
		        case "undefined":
		        case "void":
		        case "default":
		        case "prefault":
		        case "transform":
		        case "custom":
		        case "lazy":
		            return true;
		        case "string":
		        case "number":
		        case "boolean":
		        case "bigint":
		        case "symbol":
		        case "null":
		        case "never":
		        case "nan":
		        case "date":
		        case "object":
		        case "array":
		        case "tuple":
		        case "record":
		        case "map":
		        case "set":
		        case "file":
		        case "template_literal":
		            return false;
		        case "nonoptional":
		            // Normally rejects an absent key, but not when something inside supplies a value for it: `.default(v).optional().nonoptional()` accepts absence and yields v. Defer to the inner — over-reporting here only costs a presence check, and a fast path that wrongly rejects still falls back to the runtime.
		            return def.innerType ? fastPathAcceptsAbsence(def.innerType) : false;
		        case "literal":
		            return !!def.values?.includes(undefined);
		        case "enum":
		            return !!schema._zod.values?.has(undefined);
		        case "optional":
		        case "nullable":
		        case "readonly":
		        case "success":
		            return def.innerType ? fastPathAcceptsAbsence(def.innerType) : true;
		        case "catch":
		            // catch always produces a value (inner may fail → catchValue substitutes), so it accepts an absent key regardless of inner.
		            return true;
		        case "union":
		            return def.options ? def.options.some(fastPathAcceptsAbsence) : true;
		        case "intersection":
		            if (!def.left || !def.right)
		                return true;
		            return fastPathAcceptsAbsence(def.left) && fastPathAcceptsAbsence(def.right);
		        case "pipe":
		            return def.in ? fastPathAcceptsAbsence(def.in) : true;
		        default:
		            return true;
		    }
		}
		/** The middle rung permits absence without supplying anything in its place, so an absent key contributes nothing — mirrors the leading gate in `handlePropertyResult`. */
		function dropsWhenAbsent(schema) {
		    return schema._zod.optin === "optional" && schema._zod.optout === "optional";
		}
		function mayOmitUndefined(schema) {
		    return (schema._zod.optin !== "defaulted" || schema._zod.optout === "optional") && mayOutputUndefined(schema);
		}
		// whether a schema's success-path output can be undefined
		function mayOutputUndefined(schema) {
		    const def = schema._zod.def;
		    switch (def.type) {
		        case "string":
		        case "number":
		        case "boolean":
		        case "bigint":
		        case "symbol":
		        case "null":
		        case "nan":
		        case "date":
		        case "object":
		        case "array":
		        case "tuple":
		        case "record":
		        case "map":
		        case "set":
		        case "file":
		        case "template_literal":
		        case "never":
		        case "success":
		            return false;
		        case "literal":
		            return !!def.values?.includes(undefined);
		        case "enum":
		            return !!schema._zod.values?.has(undefined);
		        case "optional":
		            return true;
		        case "nullable":
		        case "readonly":
		        case "nonoptional":
		            return def.innerType ? mayOutputUndefined(def.innerType) : true;
		        case "union":
		            return def.options ? def.options.some(mayOutputUndefined) : true;
		        case "intersection":
		            return !def.left || !def.right || mayOutputUndefined(def.left) || mayOutputUndefined(def.right);
		        case "pipe":
		            return def.out ? mayOutputUndefined(def.out) : true;
		        default:
		            // any/unknown/undefined/void/default/prefault/transform/custom/lazy/catch
		            return true;
		    }
		}
		function generateNullableCheck(doc, ctx, schema, accessor, buildsValue = true) {
		    const def = schema._zod.def;
		    const outputVar = buildsValue ? newVar(ctx) : null;
		    if (outputVar)
		        doc.write(`let ${outputVar} = null;`);
		    doc.write(`if (${accessor} !== null) {`);
		    doc.indented((d) => {
		        const innerOutput = generateCheck(d, ctx, def.innerType, accessor, buildsValue);
		        if (outputVar && innerOutput !== null)
		            d.write(`${outputVar} = ${innerOutput};`);
		    });
		    doc.write(`}`);
		    return outputVar;
		}
		function generateSizeGate(doc, schema, size) {
		    for (const [max, check] of util.sizeCaps(schema._zod.def.checks)) {
		        doc.write(`if (${size} > ${numericOperand(max, check._zod.def.check)}) return INVALID;`);
		    }
		}
		function generateArrayCheck(doc, ctx, schema, accessor, buildsValue = true) {
		    const def = schema._zod.def;
		    doc.write(`if (!Array.isArray(${accessor})) return INVALID;`);
		    generateSizeGate(doc, schema, `${accessor}.length`);
		    // Build a new array with validated/transformed elements.
		    const outputVar = buildsValue ? newVar(ctx) : null;
		    const iVar = newVar(ctx);
		    const elemVar = newVar(ctx);
		    if (outputVar)
		        doc.write(`const ${outputVar} = new Array(${accessor}.length);`);
		    doc.write(`for (let ${iVar} = 0; ${iVar} < ${accessor}.length; ${iVar}++) {`);
		    doc.indented((d) => {
		        d.write(`const ${elemVar} = ${accessor}[${iVar}];`);
		        const elemOutput = compileChild(d, ctx, def.element, elemVar, buildsValue);
		        if (outputVar && elemOutput !== null)
		            d.write(`${outputVar}[${iVar}] = ${elemOutput};`);
		    });
		    doc.write(`}`);
		    return outputVar;
		}
		function generateLiteralCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    const values = def.values;
		    // Anything but a single value goes through Set.has: multi-value so every value participates, empty so nothing does — values[0] would be undefined and compile to an `!== undefined` check that accepts it. Single-value stays inlined for speed.
		    if (values.length !== 1) {
		        const literalSet = addConstant(ctx, new Set(values));
		        doc.write(`if (!${literalSet}.has(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    const value = values[0];
		    // `$ZodLiteral` matches with `values.has`, i.e. SameValueZero, so NaN matches itself. `x !== NaN` is true for every input, which would reject everything — hand it to the same Set form the multi-value path uses.
		    if (typeof value === "number" && Number.isNaN(value)) {
		        const literalSet = addConstant(ctx, new Set(values));
		        doc.write(`if (!${literalSet}.has(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    if (typeof value === "string") {
		        doc.write(`if (${accessor} !== ${util.esc(value)}) return INVALID;`);
		    }
		    else if (typeof value === "number" || typeof value === "boolean") {
		        doc.write(`if (${accessor} !== ${value}) return INVALID;`);
		    }
		    else if (value === null) {
		        doc.write(`if (${accessor} !== null) return INVALID;`);
		    }
		    else if (value === undefined) {
		        doc.write(`if (${accessor} !== undefined) return INVALID;`);
		    }
		    else if (typeof value === "bigint") {
		        doc.write(`if (${accessor} !== ${value}n) return INVALID;`);
		    }
		    else {
		        throw new ZodCompileUnsupportedError(`literal type ${typeof value}`);
		    }
		    return accessor;
		}
		function generateEnumCheck(doc, ctx, schema, accessor) {
		    const values = schema._zod.values;
		    // z.partialRecord and friends clear `_zod.values`, leaving no set to test membership against. Throw rather than emit INVALID so a union falls back whole instead of counting a false rejection.
		    if (!values) {
		        throw new ZodCompileUnsupportedError("enum schema without enumerated values");
		    }
		    const enumSet = addConstant(ctx, values);
		    doc.write(`if (!${enumSet}.has(${accessor})) return INVALID;`);
		    return accessor;
		}
		function generateWrapperCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    return generateCheck(doc, ctx, def.innerType, accessor);
		}
		function generateDefaultCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    // `defaultValue` is an accessor on schemas built through the classic/mini
		    // factories and a plain data property on ones rebuilt programmatically
		    // (deepPartial and friends). Read it off the def either way — taking only
		    // `descriptor.get` silently dropped the default for the second kind, so
		    // `.default(v)` compiled to `undefined` instead of `v`.
		    const descriptor = Object.getOwnPropertyDescriptor(schema._zod.def, "defaultValue");
		    const defaultGetter = descriptor
		        ? () => schema._zod.def.defaultValue
		        : undefined;
		    // prefault differs from default: undefined-input is first replaced with the prefault value, then run through the inner schema.
		    if (schema._zod.def.type === "prefault") {
		        if (!defaultGetter) {
		            return generateCheck(doc, ctx, def.innerType, accessor);
		        }
		        const defaultFn = addConstant(ctx, defaultGetter);
		        const inputVar = newVar(ctx);
		        doc.write(`let ${inputVar} = ${accessor};`);
		        doc.write(`if (${accessor} === undefined) ${inputVar} = ${defaultFn}();`);
		        return generateCheck(doc, ctx, def.innerType, inputVar);
		    }
		    const outputVar = newVar(ctx);
		    // Default allows undefined (replaces with default value), otherwise validates inner type
		    if (defaultGetter) {
		        const defaultFn = addConstant(ctx, defaultGetter);
		        const cloneFn = addConstant(ctx, util.shallowClone);
		        doc.write(`let ${outputVar};`);
		        doc.write(`if (${accessor} === undefined) {`);
		        doc.indented((d) => {
		            // Shallow-clone the default so callers can mutate the result without affecting subsequent parses (#5855 — also covers Map/Set).
		            d.write(`${outputVar} = ${cloneFn}(${defaultFn}());`);
		        });
		        doc.write(`} else {`);
		        doc.indented((d) => {
		            const innerOutput = generateCheck(d, ctx, def.innerType, accessor);
		            d.write(`${outputVar} = ${innerOutput} === undefined ? ${cloneFn}(${defaultFn}()) : ${innerOutput};`);
		        });
		        doc.write(`}`);
		    }
		    else {
		        doc.write(`let ${outputVar};`);
		        doc.write(`if (${accessor} !== undefined) {`);
		        doc.indented((d) => {
		            const innerOutput = generateCheck(d, ctx, def.innerType, accessor);
		            d.write(`${outputVar} = ${innerOutput};`);
		        });
		        doc.write(`}`);
		    }
		    return outputVar;
		}
		function generateNonOptionalCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    // The runtime inspects what the inner produced, not what it was given: a catch can turn a defined input into undefined, a default an absent one into a value.
		    const innerOutput = generateCheck(doc, ctx, def.innerType, accessor);
		    const outputVar = newVar(ctx);
		    doc.write(`const ${outputVar} = ${innerOutput};`);
		    doc.write(`if (${outputVar} === undefined) return INVALID;`);
		    return outputVar;
		}
		function generateTupleCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    const items = def.items;
		    const rest = def.rest;
		    doc.write(`if (!Array.isArray(${accessor})) return INVALID;`);
		    // Mirror the runtime's getTupleOptStart: find the first index where every subsequent slot accepts `undefined` (i.e. is optional on input). Anything shorter than this is too_small; trailing absent slots are legal.
		    const optinStart = getTupleOptStart(items, "optin");
		    const optoutStart = getTupleOptStart(items, "optout");
		    // Length bounds
		    if (rest) {
		        // With rest: minimum length is the last required input slot
		        doc.write(`if (${accessor}.length < ${optinStart}) return INVALID;`);
		    }
		    else {
		        // No rest: input must be in [optinStart, items.length]
		        doc.write(`if (${accessor}.length < ${optinStart} || ${accessor}.length > ${items.length}) return INVALID;`);
		    }
		    // Build the output in assignment order so absent optional-output tail slots can truncate while default/prefault slots still fill missing positions.
		    const outputVar = newVar(ctx);
		    doc.write(`const ${outputVar} = [];`);
		    // Validate and collect each fixed item
		    for (let i = 0; i < items.length; i++) {
		        const itemSchema = items[i];
		        if (i >= optoutStart) {
		            doc.write(`if (${outputVar}.length === ${i}) {`);
		            doc.indented((d) => {
		                d.write(`if (${i} < ${accessor}.length) {`);
		                d.indented((d2) => {
		                    const elemVar = newVar(ctx);
		                    d2.write(`const ${elemVar} = ${accessor}[${i}];`);
		                    const elemOutput = compileChild(d2, ctx, itemSchema, elemVar);
		                    d2.write(`${outputVar}[${i}] = ${elemOutput};`);
		                });
		                d.write(`} else {`);
		                d.indented((d2) => {
		                    // Middle rung: absence supplies nothing in its place, so truncate rather than running the item on `undefined` and keeping what it invents. Mirrors the leading gate in `handleTupleResults`.
		                    if (dropsWhenAbsent(itemSchema)) {
		                        d2.write(`${outputVar}.length = ${i};`);
		                        return;
		                    }
		                    const elemVar = newVar(ctx);
		                    const branchVar = newVar(ctx);
		                    d2.write(`const ${elemVar} = undefined;`);
		                    d2.write(`const ${branchVar} = (() => {`);
		                    d2.indented((d3) => {
		                        const elemOutput = compileChild(d3, ctx, itemSchema, elemVar);
		                        d3.write(`return ${elemOutput};`);
		                    });
		                    d2.write(`})();`);
		                    d2.write(`if (${branchVar} === INVALID || ${branchVar} === undefined) ${outputVar}.length = ${i};`);
		                    d2.write(`else ${outputVar}[${i}] = ${branchVar};`);
		                });
		                d.write(`}`);
		            });
		            doc.write(`}`);
		        }
		        else {
		            const elemVar = newVar(ctx);
		            doc.write(`const ${elemVar} = ${accessor}[${i}];`);
		            const elemOutput = compileChild(doc, ctx, itemSchema, elemVar);
		            doc.write(`${outputVar}[${i}] = ${elemOutput};`);
		        }
		    }
		    // Validate and collect rest elements if present
		    if (rest) {
		        const iVar = newVar(ctx);
		        const elemVar = newVar(ctx);
		        doc.write(`for (let ${iVar} = ${items.length}; ${iVar} < ${accessor}.length; ${iVar}++) {`);
		        doc.indented((d) => {
		            d.write(`const ${elemVar} = ${accessor}[${iVar}];`);
		            const elemOutput = compileChild(d, ctx, rest, elemVar);
		            d.write(`${outputVar}[${iVar}] = ${elemOutput};`);
		        });
		        doc.write(`}`);
		    }
		    return outputVar;
		}
		function getTupleOptStart(items, key) {
		    for (let i = items.length - 1; i >= 0; i--) {
		        // Mirrors the runtime: optin is a three-rung ladder so any rung above `undefined` permits an absent slot; optout stays two-valued.
		        const omittable = key === "optin" ? items[i]._zod.optin !== undefined : items[i]._zod.optout === "optional";
		        if (!omittable)
		            return i + 1;
		    }
		    return 0;
		}
		function generateUnionCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    const options = def.options;
		    if (def.discriminator) {
		        return generateDiscriminatedUnionCheck(doc, ctx, def, accessor);
		    }
		    // z.xor requires *exactly one* option to match. Match-counting in the fast path is only sound if every branch is exactly as strict as the runtime — any falsely-rejecting branch silently turns a multi-match rejection into an accept. Force the runtime for this rare combinator.
		    if (def.inclusive === false) {
		        throw new ZodCompileUnsupportedError("exclusive unions (z.xor)");
		    }
		    if (options.length === 0) {
		        doc.write("return INVALID;");
		        return accessor;
		    }
		    if (options.length === 1) {
		        return generateCheck(doc, ctx, options[0], accessor);
		    }
		    // Check if all options are bare literals - use Set optimization. A literal option carrying checks (e.g. .refine) must take the general path or the Set would accept values its checks reject.
		    const allLiterals = options.every((opt) => opt._zod.def.type === "literal" && !opt._zod.def.checks?.length);
		    if (allLiterals) {
		        const values = new Set(options.flatMap((opt) => opt._zod.def.values));
		        const valuesConst = addConstant(ctx, values);
		        doc.write(`if (!${valuesConst}.has(${accessor})) return INVALID;`);
		        return accessor;
		    }
		    // General case: try each option until one succeeds Use IIFEs that return the output or INVALID
		    const outputVar = newVar(ctx);
		    doc.write(`let ${outputVar};`);
		    for (let i = 0; i < options.length; i++) {
		        const opt = options[i];
		        if (i === 0) {
		            doc.write(`${outputVar} = (() => {`);
		        }
		        else {
		            doc.write(`if (${outputVar} === INVALID) ${outputVar} = (() => {`);
		        }
		        doc.indented((d) => {
		            // Generate check inside IIFE - returns INVALID on failure
		            const branchOutput = generateCheck(d, ctx, opt, accessor);
		            d.write(`return ${branchOutput};`);
		        });
		        doc.write(`})();`);
		    }
		    doc.write(`if (${outputVar} === INVALID) return INVALID;`);
		    return outputVar;
		}
		function generateDiscriminatedUnionCheck(doc, ctx, def, accessor) {
		    if (def.unionFallback) {
		        throw new ZodCompileUnsupportedError("discriminated union with unionFallback");
		    }
		    if (def.options.length === 0) {
		        doc.write("return INVALID;");
		        return accessor;
		    }
		    const discVar = newVar(ctx);
		    const outputVar = newVar(ctx);
		    doc.write(`const ${discVar} = ${accessor}?.[${util.esc(def.discriminator)}];`);
		    doc.write(`let ${outputVar};`);
		    let firstBranch = true;
		    const claimed = new Set();
		    for (const option of def.options) {
		        const values = option._zod.propValues?.[def.discriminator];
		        if (!values || values.size === 0) {
		            throw new ZodCompileUnsupportedError("discriminated union option without static discriminator values");
		        }
		        // let the interpreter handle collisions instead of compiling first-match dispatch
		        for (const value of values) {
		            if (claimed.has(value)) {
		                throw new ZodCompileUnsupportedError(`duplicate discriminator value ${String(value)}`);
		            }
		            claimed.add(value);
		        }
		        const conditions = Array.from(values, (value) => literalEquality(ctx, discVar, value));
		        const prefix = firstBranch ? "if" : "else if";
		        doc.write(`${prefix} (${conditions.join(" || ")}) {`);
		        doc.indented((d) => {
		            const branchOutput = generateCheck(d, ctx, option, accessor);
		            d.write(`${outputVar} = ${branchOutput};`);
		        });
		        doc.write(`}`);
		        firstBranch = false;
		    }
		    doc.write(`else { return INVALID; }`);
		    return outputVar;
		}
		function literalEquality(ctx, accessor, value) {
		    if (typeof value === "string")
		        return `${accessor} === ${util.esc(value)}`;
		    if (typeof value === "number") {
		        if (Number.isNaN(value))
		            return `Number.isNaN(${accessor})`;
		        return `${accessor} === ${value}`;
		    }
		    if (typeof value === "boolean")
		        return `${accessor} === ${value}`;
		    if (value === null)
		        return `${accessor} === null`;
		    if (value === undefined)
		        return `${accessor} === undefined`;
		    if (typeof value === "bigint")
		        return `${accessor} === ${value}n`;
		    if (typeof value === "symbol") {
		        const symbolConst = addConstant(ctx, value);
		        return `${accessor} === ${symbolConst}`;
		    }
		    throw new ZodCompileUnsupportedError(`literal discriminator value ${String(value)}`);
		}
		function generateIntersectionCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    // An unmergeable merge, and a child failing before the merge is even reached, both return INVALID for a case the interpreter answers with a throw.
		    ctx.definite = false;
		    const leftOutput = compileChild(doc, ctx, def.left, accessor);
		    const rightOutput = compileChild(doc, ctx, def.right, accessor);
		    // Hoist the runtime merge helper so recursive object/array merge semantics stay in one place. If the merge is invalid, return INVALID and let the runtime fallback construct canonical errors.
		    const mergeConst = addConstant(ctx, schemas_js_1.mergeValues);
		    const mergedVar = newVar(ctx);
		    doc.write(`const ${mergedVar} = ${mergeConst}(${leftOutput}, ${rightOutput});`);
		    doc.write(`if (!${mergedVar}.valid) return INVALID;`);
		    return `${mergedVar}.data`;
		}
		function generateRecordCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    // Use util.isPlainObject (rejects Date, Map, Set, class instances, etc.) to match runtime behavior. Hoisted call instead of inline so this stays a single source of truth with the runtime parser.
		    const isPlainObjectConst = addConstant(ctx, util.isPlainObject);
		    doc.write(`if (!${isPlainObjectConst}(${accessor})) return INVALID;`);
		    const outputVar = newVar(ctx);
		    const kVar = newVar(ctx);
		    const valVar = newVar(ctx);
		    doc.write(`const ${outputVar} = {};`);
		    // Exhaustive record. The runtime gates this on `values && !def.partial`, since z.partialRecord keeps its value set but makes every key optional; a loose record passes unrecognized keys through, which the per-key scan cannot express.
		    const recordDef = def;
		    const keyValues = recordDef.partial ? undefined : def.keyType._zod.values;
		    if (keyValues) {
		        const inputKeys = [];
		        for (const key of keyValues) {
		            if (!(typeof key === "string" || typeof key === "number" || typeof key === "symbol")) {
		                throw new ZodCompileUnsupportedError(`record key value ${String(key)}`);
		            }
		            const inputKey = typeof key === "number" ? key.toString() : key;
		            if (inputKey === "__proto__") {
		                // `out["__proto__"] = v` would hit the prototype setter on the output.
		                throw new ZodCompileUnsupportedError('record key "__proto__"');
		            }
		            inputKeys.push(inputKey);
		            const keyConst = addConstant(ctx, key);
		            const outKey = generateCheck(doc, ctx, def.keyType, keyConst);
		            // Read the property once. Passing the raw expression let compileChild validate one read while the output write performed a second, so a getter could hand back a value nothing had checked.
		            const valueVar = newVar(ctx);
		            doc.write(`const ${valueVar} = ${accessor}[${literalPropertyKey(ctx, inputKey)}];`);
		            const valOutput = compileChild(doc, ctx, def.valueType, valueVar);
		            doc.write(`${outputVar}[${outKey}] = ${valOutput};`);
		        }
		        // `mode: "loose"` changes only what happens to unrecognized keys, so it belongs here rather than the per-present-key scan. Passing them through matches the runtime, which skips `__proto__`.
		        const knownKeysConst = addConstant(ctx, new Set(inputKeys));
		        doc.write(`for (const ${kVar} in ${accessor}) {`);
		        doc.indented((d) => {
		            d.write(`if (${knownKeysConst}.has(${kVar})) continue;`);
		            if (recordDef.mode === "loose") {
		                d.write(`if (${kVar} !== "__proto__") ${outputVar}[${kVar}] = ${accessor}[${kVar}];`);
		            }
		            else {
		                d.write(`return INVALID;`);
		            }
		        });
		        doc.write(`}`);
		        return outputVar;
		    }
		    // The bare-string shortcut below only tests `typeof key === "string"`, so it
		    // is correct exclusively for a `z.string()` carrying nothing else. A string
		    // *format* lives on the def rather than in `checks` — `z.record(z.email(), …)`
		    // reads as a plain string here — and coercion rewrites the key, so both have
		    // to take the general path or the shortcut accepts keys the runtime rejects.
		    const keyDef = def.keyType._zod.def;
		    const keyIsBareString = keyDef.type === "string" && keyDef.format === undefined && !keyDef.coerce && (keyDef.checks?.length ?? 0) === 0;
		    if (!keyIsBareString) {
		        // A key schema with no value set is a constraint every key must satisfy
		        // (`z.email()`, `z.string().min(3)`, `z.number()`, a template literal).
		        // The runtime runs it against each own enumerable key and writes the value
		        // under the key it produced, so compile it once and call it per key.
		        const isLoose = def.mode === "loose";
		        // the key compiles in its own context, so carry its verdict out: a key schema that can answer INVALID undecidably makes this validator undecidable too
		        const keyFn = compileFn(def.keyType);
		        if (keyFn.definite === false)
		            ctx.definite = false;
		        const keyFast = addConstant(ctx, keyFn);
		        const numericConst = addConstant(ctx, regexes.number);
		        const outKeyVar = newVar(ctx);
		        // the body runs once per string key and once per symbol key, since a key schema can accept symbols
		        emitOwnKeys(doc, ctx, accessor, kVar, (d) => {
		            d.write(`let ${outKeyVar} = ${keyFast}(${kVar});`);
		            // Numeric-string retry, mirroring the runtime: a key the schema rejects as a string is tried again as a number, so z.record(z.number(), …) matches the numeric keys JavaScript stringified on the way in.
		            d.write(`if (${outKeyVar} === INVALID && typeof ${kVar} === "string" && ${numericConst}.test(${kVar})) ${outKeyVar} = ${keyFast}(Number(${kVar}));`);
		            if (isLoose) {
		                // A loose record keeps a key its schema rejects, copying the value across unvalidated rather than failing the parse.
		                d.write(`if (${outKeyVar} === INVALID) { ${outputVar}[${kVar}] = ${accessor}[${kVar}]; continue; }`);
		            }
		            else {
		                d.write(`if (${outKeyVar} === INVALID) return INVALID;`);
		            }
		            // The guard above tested the input key, but the schema can normalize an ordinary key into __proto__; re-check the one actually written under.
		            d.write(`if (${outKeyVar} === "__proto__") continue;`);
		            // Read once: the raw expression would be evaluated again by the output write below, so an accessor could return an unvalidated second value.
		            const valueVar = newVar(ctx);
		            d.write(`const ${valueVar} = ${accessor}[${kVar}];`);
		            const valOutput = compileChild(d, ctx, def.valueType, valueVar);
		            d.write(`${outputVar}[${outKeyVar}] = ${valOutput};`);
		        });
		        return outputVar;
		    }
		    // Plain z.string() keys: every own enumerable string key's value is validated, and an own enumerable symbol key fails the string key schema, as it does in the runtime's Reflect.ownKeys walk.
		    emitOwnKeys(doc, ctx, accessor, kVar, (d) => {
		        d.write(`const ${valVar} = ${accessor}[${kVar}];`);
		        const valOutput = compileChild(d, ctx, def.valueType, valVar);
		        d.write(`${outputVar}[${kVar}] = ${valOutput};`);
		    }, `return INVALID;`);
		    return outputVar;
		}
		// Walks the own enumerable keys of a plain object in Reflect.ownKeys order — strings from getOwnPropertyNames, then symbols — instead of Reflect.ownKeys, whose accumulator made that walk 3–6x the cost of the loop it fed. Both snapshots are taken before any value is read and every key is rechecked with propertyIsEnumerable when visited, exactly the runtime's walk, so a getter that adds, deletes, hides or reveals a key mid-walk sees the runtime's verdict; for-in and Object.keys were no faster and each lost a case (for-in enumerates the prototype chain after the own keys, Object.keys drops a key that is non-enumerable at snapshot time). `body` is written once for the string loop and once for the symbol loop unless `onSymbol` replaces the latter.
		function emitOwnKeys(doc, ctx, accessor, kVar, body, onSymbol) {
		    const propIsEnumerableConst = addConstant(ctx, Object.prototype.propertyIsEnumerable);
		    const symsVar = newVar(ctx);
		    const keysVar = newVar(ctx);
		    const iVar = newVar(ctx);
		    doc.write(`const ${symsVar} = Object.getOwnPropertySymbols(${accessor});`);
		    doc.write(`const ${keysVar} = Object.getOwnPropertyNames(${accessor});`);
		    doc.write(`for (let ${iVar} = 0; ${iVar} < ${keysVar}.length; ${iVar}++) {`);
		    doc.indented((d) => {
		        d.write(`const ${kVar} = ${keysVar}[${iVar}];`);
		        d.write(`if (${kVar} === "__proto__" || !${propIsEnumerableConst}.call(${accessor}, ${kVar})) continue;`);
		        body(d);
		    });
		    doc.write(`}`);
		    doc.write(`for (let ${iVar} = 0; ${iVar} < ${symsVar}.length; ${iVar}++) {`);
		    doc.indented((d) => {
		        d.write(`const ${kVar} = ${symsVar}[${iVar}];`);
		        d.write(`if (!${propIsEnumerableConst}.call(${accessor}, ${kVar})) continue;`);
		        if (onSymbol)
		            d.write(onSymbol);
		        else
		            body(d);
		    });
		    doc.write(`}`);
		}
		function literalPropertyKey(ctx, key) {
		    if (typeof key === "string")
		        return util.esc(key);
		    return addConstant(ctx, key);
		}
		function generateMapCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    doc.write(`if (!(${accessor} instanceof Map)) return INVALID;`);
		    generateSizeGate(doc, schema, `${accessor}.size`);
		    const outputVar = newVar(ctx);
		    const kVar = newVar(ctx);
		    const valVar = newVar(ctx);
		    doc.write(`const ${outputVar} = new Map();`);
		    doc.write(`for (const [${kVar}, ${valVar}] of ${accessor}) {`);
		    doc.indented((d) => {
		        const keyOutput = generateCheck(d, ctx, def.keyType, kVar);
		        const valOutput = generateCheck(d, ctx, def.valueType, valVar);
		        d.write(`${outputVar}.set(${keyOutput}, ${valOutput});`);
		    });
		    doc.write(`}`);
		    return outputVar;
		}
		function generateSetCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    doc.write(`if (!(${accessor} instanceof Set)) return INVALID;`);
		    generateSizeGate(doc, schema, `${accessor}.size`);
		    const outputVar = newVar(ctx);
		    const valVar = newVar(ctx);
		    doc.write(`const ${outputVar} = new Set();`);
		    doc.write(`for (const ${valVar} of ${accessor}) {`);
		    doc.indented((d) => {
		        const valOutput = generateCheck(d, ctx, def.valueType, valVar);
		        d.write(`${outputVar}.add(${valOutput});`);
		    });
		    doc.write(`}`);
		    return outputVar;
		}
		function generateFileCheck(doc, accessor) {
		    // Runtime $ZodFile is a bare `instanceof File`, including the implicit global lookup. The previous duck-typed fallback accepted arbitrary {name, size} objects in File-less environments — behavior the runtime never had.
		    doc.write(`if (!(${accessor} instanceof File)) return INVALID;`);
		    return accessor;
		}
		function generateTemplateLiteralCheck(doc, ctx, schema, accessor) {
		    doc.write(`if (typeof ${accessor} !== "string") return INVALID;`);
		    // Template literal schemas have a pre-computed pattern in _zod.pattern
		    const pattern = schema._zod.pattern;
		    if (pattern) {
		        const patternConst = addConstant(ctx, pattern);
		        doc.write(`${patternConst}.lastIndex = 0;`);
		        doc.write(`if (!${patternConst}.test(${accessor})) return INVALID;`);
		    }
		    return accessor;
		}
		function generateLazyCheck(doc, ctx, schema, accessor) {
		    // For lazy schemas, we use a cached parser that falls back to runtime Zod parsing This handles recursive schemas correctly by avoiding infinite compilation loops
		    const def = schema._zod.def;
		    const getterConst = addUserConstant(ctx, def.getter);
		    const cacheConst = addConstant(ctx, { parser: null });
		    doc.write(`if (!${cacheConst}.parser) {`);
		    doc.indented((d) => {
		        d.write(`const inner = ${getterConst}();`);
		        d.write(`${cacheConst}.parser = function(input) {`);
		        d.indented((d2) => {
		            // Use runtime Zod parsing - this correctly handles recursive schemas Pass an empty ctx like runtimeRun does — runtime parsers read ctx.skipChecks/ctx.direction unconditionally and crash on undefined.
		            d2.write(`const result = inner._zod.run({ value: input, issues: [] }, {});`);
		            d2.write(`return result.issues.length === 0 ? result.value : INVALID;`);
		        });
		        d.write(`};`);
		    });
		    doc.write(`}`);
		    const outputVar = newVar(ctx);
		    doc.write(`const ${outputVar} = ${cacheConst}.parser(${accessor});`);
		    doc.write(`if (${outputVar} === INVALID) return INVALID;`);
		    return outputVar;
		}
		function generatePipeCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    // Validate input type first
		    const inputOutput = generateCheck(doc, ctx, def.in, accessor);
		    if (def.transform) {
		        // Apply transform and validate output. The transform may read its second `payload` argument (codec transforms like z.stringbool() push issues there) so wrap the call in a helper that spoofs a payload. Pushed issues signal INVALID and the wrapper falls back to the runtime, and a plain function handing back a promise answers INVALID too — union parity, but not a decidable rejection at the top level.
		        if (isAsyncFunction(def.transform)) {
		            throw new ZodCompileAsyncError("z.compile: async transforms in pipes are not supported");
		        }
		        const transformFn = def.transform;
		        const helperFn = (value) => {
		            // `addIssue` has to be here: a transform reporting through it is reporting, not failing. Without it the call threw a TypeError that the old catch swallowed into a fallback, so every ctx.addIssue transform quietly lost its fast path and the real error was never visible.
		            const fakePayload = { value, issues: [], addIssue: pushIssue };
		            // A throw is deliberately not caught. The interpreter lets one propagate out of the whole parse; swallowing it into INVALID turned a thrown error into a merely-rejected union branch, so a later branch answered instead.
		            const result = transformFn(value, fakePayload);
		            if (result instanceof Promise)
		                return exports.INVALID;
		            return fakePayload.issues.length === 0 ? result : exports.INVALID;
		        };
		        const helperConst = addUserConstant(ctx, helperFn);
		        const transformedVar = newVar(ctx);
		        doc.write(`const ${transformedVar} = ${helperConst}(${inputOutput});`);
		        doc.write(`if (${transformedVar} === INVALID) return INVALID;`);
		        return generateCheck(doc, ctx, def.out, transformedVar);
		    }
		    else {
		        // No transform - validate output type on same value
		        return generateCheck(doc, ctx, def.out, inputOutput);
		    }
		}
		function isAsyncFunction(fn) {
		    return (typeof fn === "function" &&
		        (fn.constructor.name === "AsyncFunction" ||
		            fn[Symbol.toStringTag] === "AsyncFunction"));
		}
		function generateCustomCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    if (def.fn) {
		        // Check for async function
		        if (isAsyncFunction(def.fn)) {
		            throw new ZodCompileAsyncError("z.compile: async custom predicates are not supported");
		        }
		        // Custom schema with a predicate function (e.g. z.instanceof). `isAsyncFunction` above is syntactic, so a plain function returning a promise reaches here, and a promise is truthy — it would read as a pass where the interpreter throws.
		        const fnConst = addUserConstant(ctx, def.fn);
		        const throwAsyncConst = addConstant(ctx, throwAsync);
		        const resVar = newVar(ctx);
		        doc.write(`const ${resVar} = ${fnConst}(${accessor});`);
		        doc.write(`if (${resVar} instanceof Promise) ${throwAsyncConst}();`);
		        doc.write(`if (!${resVar}) return INVALID;`);
		    }
		    else {
		        throw new ZodCompileUnsupportedError("custom schema without a predicate function");
		    }
		    return accessor;
		}
		// Runtime helper for a compiled `catch`: runs the inner schema once and returns its value when it succeeded. Anything else — a failure the catch would handle, or an async inner — returns INVALID so the interpreter takes over.
		function runtimeCatch(innerSchema, catchValue, value) {
		    const result = innerSchema._zod.run({ value, issues: [] }, {});
		    if (result && typeof result.then === "function")
		        return exports.INVALID;
		    const r = result;
		    if (r.issues.length === 0)
		        return r.value;
		    // Only reached for a catch value that ignores the parse context — codegen refuses the rest — so there are no issues to finalize and nothing here needs the caller's error map.
		    return catchValue();
		}
		function generateCatchCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    // An untagged catchValue is a user callback, and one reading `ctx.error` needs the caller's per-parse error map. Catch *succeeds*, so a wrong message there is unobservable — refuse at codegen, and not islandable either, since `runtimeRun` has no context to hand it.
		    if (!def.catchValue[util.CONSTANT_CATCH]) {
		        throw new ZodCompileUnsupportedError("catch with a callback (only a constant catch value compiles)", false);
		    }
		    const outputVar = newVar(ctx);
		    doc.write(`let ${outputVar} = (() => {`);
		    doc.indented((d) => {
		        const innerOut = compileChild(d, ctx, def.innerType, accessor);
		        d.write(`return ${innerOut};`);
		    });
		    doc.write(`})();`);
		    const innerConst = addConstant(ctx, def.innerType);
		    const catchConst = addUserConstant(ctx, def.catchValue);
		    const catchHelperConst = addConstant(ctx, runtimeCatch);
		    doc.write(`if (${outputVar} === INVALID) {`);
		    doc.indented((d) => {
		        d.write(`${outputVar} = ${catchHelperConst}(${innerConst}, ${catchConst}, ${accessor});`);
		        d.write(`if (${outputVar} === INVALID) return INVALID;`);
		    });
		    doc.write(`}`);
		    return outputVar;
		}
		function generateTransformCheck(doc, ctx, schema, accessor) {
		    const def = schema._zod.def;
		    if (def.transform) {
		        // Check for async transform
		        if (isAsyncFunction(def.transform)) {
		            throw new ZodCompileAsyncError("z.compile: async transforms are not supported");
		        }
		        // Create a helper that runs the transform and returns the result or INVALID on error
		        const transformFn = def.transform;
		        const helperFn = (value) => {
		            const fakePayload = { value, issues: [], addIssue: pushIssue };
		            // As in the pipe helper: a throw propagates, because the interpreter lets it out of the whole parse rather than treating it as a failed branch.
		            const result = transformFn(value, fakePayload);
		            if (result instanceof Promise)
		                return exports.INVALID;
		            return fakePayload.issues.length === 0 ? result : exports.INVALID;
		        };
		        const helperConst = addUserConstant(ctx, helperFn);
		        const outputVar = newVar(ctx);
		        doc.write(`const ${outputVar} = ${helperConst}(${accessor});`);
		        doc.write(`if (${outputVar} === INVALID) return INVALID;`);
		        return outputVar;
		    }
		    return accessor;
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (compile$3));
	return compile$3;
}

var api = {};

var hasRequiredApi;

function requireApi () {
	if (hasRequiredApi) return api;
	hasRequiredApi = 1;
	(function (exports) {
		var __createBinding = (api && api.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (api && api.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (api && api.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TimePrecision = void 0;
		exports._string = _string;
		exports._coercedString = _coercedString;
		exports._email = _email;
		exports._guid = _guid;
		exports._uuid = _uuid;
		exports._uuidv4 = _uuidv4;
		exports._uuidv6 = _uuidv6;
		exports._uuidv7 = _uuidv7;
		exports._url = _url;
		exports._emoji = _emoji;
		exports._nanoid = _nanoid;
		exports._cuid = _cuid;
		exports._cuid2 = _cuid2;
		exports._ulid = _ulid;
		exports._xid = _xid;
		exports._ksuid = _ksuid;
		exports._ipv4 = _ipv4;
		exports._ipv6 = _ipv6;
		exports._mac = _mac;
		exports._cidrv4 = _cidrv4;
		exports._cidrv6 = _cidrv6;
		exports._base64 = _base64;
		exports._base64url = _base64url;
		exports._e164 = _e164;
		exports._creditCard = _creditCard;
		exports._iban = _iban;
		exports._jwt = _jwt;
		exports._isoDateTime = _isoDateTime;
		exports._isoDate = _isoDate;
		exports._isoTime = _isoTime;
		exports._isoDuration = _isoDuration;
		exports._number = _number;
		exports._coercedNumber = _coercedNumber;
		exports._int = _int;
		exports._float32 = _float32;
		exports._float64 = _float64;
		exports._int32 = _int32;
		exports._uint32 = _uint32;
		exports._boolean = _boolean;
		exports._coercedBoolean = _coercedBoolean;
		exports._bigint = _bigint;
		exports._coercedBigint = _coercedBigint;
		exports._int64 = _int64;
		exports._uint64 = _uint64;
		exports._symbol = _symbol;
		exports._undefined = _undefined;
		exports._null = _null;
		exports._any = _any;
		exports._unknown = _unknown;
		exports._never = _never;
		exports._void = _void;
		exports._date = _date;
		exports._coercedDate = _coercedDate;
		exports._nan = _nan;
		exports._lt = _lt;
		exports._lte = _lte;
		exports._max = _lte;
		exports._lte = _lte;
		exports._max = _lte;
		exports._gt = _gt;
		exports._gte = _gte;
		exports._min = _gte;
		exports._gte = _gte;
		exports._min = _gte;
		exports._positive = _positive;
		exports._negative = _negative;
		exports._nonpositive = _nonpositive;
		exports._nonnegative = _nonnegative;
		exports._multipleOf = _multipleOf;
		exports._maxSize = _maxSize;
		exports._minSize = _minSize;
		exports._size = _size;
		exports._maxLength = _maxLength;
		exports._minLength = _minLength;
		exports._length = _length;
		exports._regex = _regex;
		exports._lowercase = _lowercase;
		exports._uppercase = _uppercase;
		exports._includes = _includes;
		exports._startsWith = _startsWith;
		exports._endsWith = _endsWith;
		exports._property = _property;
		exports._properties = _properties;
		exports._mime = _mime;
		exports._overwrite = _overwrite;
		exports._normalize = _normalize;
		exports._trim = _trim;
		exports._toLowerCase = _toLowerCase;
		exports._toUpperCase = _toUpperCase;
		exports._slugify = _slugify;
		exports._array = _array;
		exports._union = _union;
		exports._xor = _xor;
		exports._discriminatedUnion = _discriminatedUnion;
		exports._intersection = _intersection;
		exports._tuple = _tuple;
		exports._record = _record;
		exports._map = _map;
		exports._set = _set;
		exports._enum = _enum;
		exports._nativeEnum = _nativeEnum;
		exports._literal = _literal;
		exports._file = _file;
		exports._transform = _transform;
		exports._optional = _optional;
		exports._nullable = _nullable;
		exports._default = _default;
		exports._nonoptional = _nonoptional;
		exports._success = _success;
		exports._catch = _catch;
		exports._pipe = _pipe;
		exports._readonly = _readonly;
		exports._templateLiteral = _templateLiteral;
		exports._lazy = _lazy;
		exports._promise = _promise;
		exports._custom = _custom;
		exports._refine = _refine;
		exports._superRefine = _superRefine;
		exports._check = _check;
		exports.describe = describe;
		exports.meta = meta;
		exports._stringbool = _stringbool;
		exports._stringFormat = _stringFormat;
		const checks = __importStar(/*@__PURE__*/ requireChecks$1());
		const registries = __importStar(/*@__PURE__*/ requireRegistries());
		const schemas = __importStar(/*@__PURE__*/ requireSchemas$1());
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		function snapshotChecks(def) {
		    if (def.checks)
		        def.checks = [...def.checks];
		    return def;
		}
		// @__NO_SIDE_EFFECTS__
		function _string(Class, params) {
		    return new Class(snapshotChecks({ type: "string", ...util.normalizeParams(params) }));
		}
		// @__NO_SIDE_EFFECTS__
		function _coercedString(Class, params) {
		    return new Class(snapshotChecks({ type: "string", coerce: true, ...util.normalizeParams(params) }));
		}
		// @__NO_SIDE_EFFECTS__
		function _email(Class, params) {
		    return new Class({
		        type: "string",
		        format: "email",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _guid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "guid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uuid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "uuid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uuidv4(Class, params) {
		    return new Class({
		        type: "string",
		        format: "uuid",
		        check: "string_format",
		        abort: false,
		        version: "v4",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uuidv6(Class, params) {
		    return new Class({
		        type: "string",
		        format: "uuid",
		        check: "string_format",
		        abort: false,
		        version: "v6",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uuidv7(Class, params) {
		    return new Class({
		        type: "string",
		        format: "uuid",
		        check: "string_format",
		        abort: false,
		        version: "v7",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _url(Class, params) {
		    return new Class({
		        type: "string",
		        format: "url",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _emoji(Class, params) {
		    return new Class({
		        type: "string",
		        format: "emoji",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _nanoid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "nanoid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		/**
		 * @deprecated CUID v1 is deprecated by its authors due to information leakage
		 * (timestamps embedded in the id). Use {@link _cuid2} instead.
		 * See https://github.com/paralleldrive/cuid.
		 */
		// @__NO_SIDE_EFFECTS__
		function _cuid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "cuid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _cuid2(Class, params) {
		    return new Class({
		        type: "string",
		        format: "cuid2",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _ulid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "ulid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _xid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "xid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _ksuid(Class, params) {
		    return new Class({
		        type: "string",
		        format: "ksuid",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _ipv4(Class, params) {
		    return new Class({
		        type: "string",
		        format: "ipv4",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _ipv6(Class, params) {
		    return new Class({
		        type: "string",
		        format: "ipv6",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _mac(Class, params) {
		    return new Class({
		        type: "string",
		        format: "mac",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _cidrv4(Class, params) {
		    return new Class({
		        type: "string",
		        format: "cidrv4",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _cidrv6(Class, params) {
		    return new Class({
		        type: "string",
		        format: "cidrv6",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _base64(Class, params) {
		    return new Class({
		        type: "string",
		        format: "base64",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _base64url(Class, params) {
		    return new Class({
		        type: "string",
		        format: "base64url",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _e164(Class, params) {
		    return new Class({
		        type: "string",
		        format: "e164",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _creditCard(Class, params) {
		    return new Class({
		        type: "string",
		        format: "credit_card",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _iban(Class, params) {
		    return new Class({
		        type: "string",
		        format: "iban",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _jwt(Class, params) {
		    return new Class({
		        type: "string",
		        format: "jwt",
		        check: "string_format",
		        abort: false,
		        ...util.normalizeParams(params),
		    });
		}
		exports.TimePrecision = {
		    Any: null,
		    Minute: -1,
		    Second: 0,
		    Millisecond: 3,
		    Microsecond: 6,
		};
		// @__NO_SIDE_EFFECTS__
		function _isoDateTime(Class, params) {
		    return new Class({
		        type: "string",
		        format: "datetime",
		        check: "string_format",
		        offset: false,
		        local: false,
		        precision: null,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _isoDate(Class, params) {
		    return new Class({
		        type: "string",
		        format: "date",
		        check: "string_format",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _isoTime(Class, params) {
		    return new Class({
		        type: "string",
		        format: "time",
		        check: "string_format",
		        precision: null,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _isoDuration(Class, params) {
		    return new Class({
		        type: "string",
		        format: "duration",
		        check: "string_format",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _number(Class, params) {
		    return new Class(snapshotChecks({ type: "number", checks: [], ...util.normalizeParams(params) }));
		}
		// @__NO_SIDE_EFFECTS__
		function _coercedNumber(Class, params) {
		    return new Class(snapshotChecks({ type: "number", coerce: true, checks: [], ...util.normalizeParams(params) }));
		}
		// @__NO_SIDE_EFFECTS__
		function _int(Class, params) {
		    return new Class({
		        type: "number",
		        check: "number_format",
		        abort: false,
		        format: "safeint",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _float32(Class, params) {
		    return new Class({
		        type: "number",
		        check: "number_format",
		        abort: false,
		        format: "float32",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _float64(Class, params) {
		    return new Class({
		        type: "number",
		        check: "number_format",
		        abort: false,
		        format: "float64",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _int32(Class, params) {
		    return new Class({
		        type: "number",
		        check: "number_format",
		        abort: false,
		        format: "int32",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uint32(Class, params) {
		    return new Class({
		        type: "number",
		        check: "number_format",
		        abort: false,
		        format: "uint32",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _boolean(Class, params) {
		    return new Class({
		        type: "boolean",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _coercedBoolean(Class, params) {
		    return new Class({
		        type: "boolean",
		        coerce: true,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _bigint(Class, params) {
		    return new Class({
		        type: "bigint",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _coercedBigint(Class, params) {
		    return new Class({
		        type: "bigint",
		        coerce: true,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _int64(Class, params) {
		    return new Class({
		        type: "bigint",
		        check: "bigint_format",
		        abort: false,
		        format: "int64",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uint64(Class, params) {
		    return new Class({
		        type: "bigint",
		        check: "bigint_format",
		        abort: false,
		        format: "uint64",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _symbol(Class, params) {
		    return new Class({
		        type: "symbol",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _undefined(Class, params) {
		    return new Class({
		        type: "undefined",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _null(Class, params) {
		    return new Class({
		        type: "null",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _any(Class) {
		    return new Class({
		        type: "any",
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _unknown(Class) {
		    return new Class({
		        type: "unknown",
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _never(Class, params) {
		    return new Class({
		        type: "never",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _void(Class, params) {
		    return new Class({
		        type: "void",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _date(Class, params) {
		    return new Class({
		        type: "date",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _coercedDate(Class, params) {
		    return new Class({
		        type: "date",
		        coerce: true,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _nan(Class, params) {
		    return new Class({
		        type: "nan",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _lt(value, params) {
		    return new checks.$ZodCheckLessThan({
		        check: "less_than",
		        ...util.normalizeParams(params),
		        value,
		        inclusive: false,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _lte(value, params) {
		    return new checks.$ZodCheckLessThan({
		        check: "less_than",
		        ...util.normalizeParams(params),
		        value,
		        inclusive: true,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _gt(value, params) {
		    return new checks.$ZodCheckGreaterThan({
		        check: "greater_than",
		        ...util.normalizeParams(params),
		        value,
		        inclusive: false,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _gte(value, params) {
		    return new checks.$ZodCheckGreaterThan({
		        check: "greater_than",
		        ...util.normalizeParams(params),
		        value,
		        inclusive: true,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _positive(params) {
		    return _gt(0, params);
		}
		// negative
		// @__NO_SIDE_EFFECTS__
		function _negative(params) {
		    return _lt(0, params);
		}
		// nonpositive
		// @__NO_SIDE_EFFECTS__
		function _nonpositive(params) {
		    return _lte(0, params);
		}
		// nonnegative
		// @__NO_SIDE_EFFECTS__
		function _nonnegative(params) {
		    return _gte(0, params);
		}
		// @__NO_SIDE_EFFECTS__
		function _multipleOf(value, params) {
		    return new checks.$ZodCheckMultipleOf({
		        check: "multiple_of",
		        ...util.normalizeParams(params),
		        value,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _maxSize(maximum, params) {
		    return new checks.$ZodCheckMaxSize({
		        check: "max_size",
		        ...util.normalizeParams(params),
		        maximum,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _minSize(minimum, params) {
		    return new checks.$ZodCheckMinSize({
		        check: "min_size",
		        ...util.normalizeParams(params),
		        minimum,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _size(size, params) {
		    return new checks.$ZodCheckSizeEquals({
		        check: "size_equals",
		        ...util.normalizeParams(params),
		        size,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _maxLength(maximum, params) {
		    const ch = new checks.$ZodCheckMaxLength({
		        check: "max_length",
		        ...util.normalizeParams(params),
		        maximum,
		    });
		    return ch;
		}
		// @__NO_SIDE_EFFECTS__
		function _minLength(minimum, params) {
		    return new checks.$ZodCheckMinLength({
		        check: "min_length",
		        ...util.normalizeParams(params),
		        minimum,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _length(length, params) {
		    return new checks.$ZodCheckLengthEquals({
		        check: "length_equals",
		        ...util.normalizeParams(params),
		        length,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _regex(pattern, params) {
		    return new checks.$ZodCheckRegex({
		        check: "string_format",
		        format: "regex",
		        ...util.normalizeParams(params),
		        pattern,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _lowercase(params) {
		    return new checks.$ZodCheckLowerCase({
		        check: "string_format",
		        format: "lowercase",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _uppercase(params) {
		    return new checks.$ZodCheckUpperCase({
		        check: "string_format",
		        format: "uppercase",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _includes(includes, params) {
		    return new checks.$ZodCheckIncludes({
		        check: "string_format",
		        format: "includes",
		        ...util.normalizeParams(params),
		        includes,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _startsWith(prefix, params) {
		    return new checks.$ZodCheckStartsWith({
		        check: "string_format",
		        format: "starts_with",
		        ...util.normalizeParams(params),
		        prefix,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _endsWith(suffix, params) {
		    return new checks.$ZodCheckEndsWith({
		        check: "string_format",
		        format: "ends_with",
		        ...util.normalizeParams(params),
		        suffix,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _property(property, schema, params) {
		    return new checks.$ZodCheckProperty({
		        check: "property",
		        property,
		        schema,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _properties(shape, params) {
		    return new checks.$ZodCheckProperties({
		        check: "properties",
		        shape,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _mime(types, params) {
		    return new checks.$ZodCheckMimeType({
		        check: "mime_type",
		        mime: types,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _overwrite(tx) {
		    return new checks.$ZodCheckOverwrite({
		        check: "overwrite",
		        tx,
		    });
		}
		// normalize
		// @__NO_SIDE_EFFECTS__
		function _normalize(form) {
		    return _overwrite((input) => input.normalize(form));
		}
		// trim
		// @__NO_SIDE_EFFECTS__
		function _trim() {
		    return _overwrite((input) => input.trim());
		}
		// toLowerCase
		// @__NO_SIDE_EFFECTS__
		function _toLowerCase() {
		    return _overwrite((input) => input.toLowerCase());
		}
		// toUpperCase
		// @__NO_SIDE_EFFECTS__
		function _toUpperCase() {
		    return _overwrite((input) => input.toUpperCase());
		}
		// slugify
		// @__NO_SIDE_EFFECTS__
		function _slugify() {
		    return _overwrite((input) => util.slugify(input));
		}
		// @__NO_SIDE_EFFECTS__
		function _array(Class, element, params) {
		    return new Class({
		        type: "array",
		        element,
		        // get element() {
		        //   return element;
		        // },
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _union(Class, options, params) {
		    return new Class({
		        type: "union",
		        options,
		        ...util.normalizeParams(params),
		    });
		}
		function _xor(Class, options, params) {
		    return new Class({
		        type: "union",
		        options,
		        inclusive: false,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _discriminatedUnion(Class, discriminator, options, params) {
		    return new Class({
		        type: "union",
		        options: options,
		        discriminator,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _intersection(Class, left, right) {
		    return new Class({
		        type: "intersection",
		        left,
		        right,
		    });
		}
		// export function _tuple(
		//   Class: util.SchemaClass<schemas.$ZodTuple>,
		//   items: [],
		//   params?: string | $ZodTupleParams
		// ): schemas.$ZodTuple<[], null>;
		// @__NO_SIDE_EFFECTS__
		function _tuple(Class, items, _paramsOrRest, _params) {
		    const hasRest = _paramsOrRest instanceof schemas.$ZodType;
		    const params = hasRest ? _params : _paramsOrRest;
		    const rest = hasRest ? _paramsOrRest : null;
		    return new Class({
		        type: "tuple",
		        items,
		        rest,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _record(Class, keyType, valueType, params) {
		    return new Class({
		        type: "record",
		        keyType,
		        valueType,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _map(Class, keyType, valueType, params) {
		    return new Class({
		        type: "map",
		        keyType,
		        valueType,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _set(Class, valueType, params) {
		    return new Class({
		        type: "set",
		        valueType,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _enum(Class, values, params) {
		    const entries = Array.isArray(values) ? Object.fromEntries(values.map((v) => [v, v])) : values;
		    // if (Array.isArray(values)) {
		    //   for (const value of values) {
		    //     entries[value] = value;
		    //   }
		    // } else {
		    //   Object.assign(entries, values);
		    // }
		    // const entries: util.EnumLike = {};
		    // for (const val of values) {
		    //   entries[val] = val;
		    // }
		    return new Class({
		        type: "enum",
		        entries,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		/** @deprecated This API has been merged into `z.enum()`. Use `z.enum()` instead.
		 *
		 * ```ts
		 * enum Colors { red, green, blue }
		 * z.enum(Colors);
		 * ```
		 */
		function _nativeEnum(Class, entries, params) {
		    return new Class({
		        type: "enum",
		        entries,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _literal(Class, value, params) {
		    return new Class({
		        type: "literal",
		        values: Array.isArray(value) ? value : [value],
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _file(Class, params) {
		    return new Class({
		        type: "file",
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _transform(Class, fn) {
		    return new Class({
		        type: "transform",
		        transform: fn,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _optional(Class, innerType) {
		    return new Class({
		        type: "optional",
		        innerType,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _nullable(Class, innerType) {
		    return new Class({
		        type: "nullable",
		        innerType,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _default(Class, innerType, defaultValue) {
		    return new Class({
		        type: "default",
		        innerType,
		        get defaultValue() {
		            return typeof defaultValue === "function" ? defaultValue() : util.shallowClone(defaultValue);
		        },
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _nonoptional(Class, innerType, params) {
		    return new Class({
		        type: "nonoptional",
		        innerType,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _success(Class, innerType) {
		    return new Class({
		        type: "success",
		        innerType,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _catch(Class, innerType, catchValue) {
		    return new Class({
		        type: "catch",
		        innerType,
		        catchValue: (typeof catchValue === "function" ? catchValue : util.constantCatch(catchValue)),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _pipe(Class, in_, out) {
		    return new Class({
		        type: "pipe",
		        in: in_,
		        out,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _readonly(Class, innerType) {
		    return new Class({
		        type: "readonly",
		        innerType,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _templateLiteral(Class, parts, params) {
		    return new Class({
		        type: "template_literal",
		        parts,
		        ...util.normalizeParams(params),
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _lazy(Class, getter) {
		    return new Class({
		        type: "lazy",
		        getter,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _promise(Class, innerType) {
		    return new Class({
		        type: "promise",
		        innerType,
		    });
		}
		// @__NO_SIDE_EFFECTS__
		function _custom(Class, fn, _params) {
		    const norm = util.normalizeParams(_params);
		    norm.abort ?? (norm.abort = true); // default to abort:false
		    const schema = new Class({
		        type: "custom",
		        check: "custom",
		        fn: fn,
		        ...norm,
		    });
		    return schema;
		}
		// same as _custom but defaults to abort:false
		// @__NO_SIDE_EFFECTS__
		function _refine(Class, fn, _params) {
		    const schema = new Class({
		        type: "custom",
		        check: "custom",
		        fn: fn,
		        ...util.normalizeParams(_params),
		    });
		    return schema;
		}
		// @__NO_SIDE_EFFECTS__
		function _superRefine(fn, params) {
		    const ch = _check((payload) => {
		        payload.addIssue = (issue) => {
		            if (typeof issue === "string") {
		                payload.issues.push(util.issue(issue, payload.value, ch._zod.def));
		            }
		            else {
		                // for Zod 3 backwards compatibility
		                const _issue = issue;
		                if (_issue.fatal)
		                    _issue.continue = false;
		                _issue.code ?? (_issue.code = "custom");
		                if (!("input" in _issue))
		                    _issue.input = payload.value;
		                _issue.inst ?? (_issue.inst = ch);
		                _issue.continue ?? (_issue.continue = !ch._zod.def.abort); // abort is always undefined, so this is always true...
		                payload.issues.push(util.issue(_issue));
		            }
		        };
		        return fn(payload.value, payload);
		    }, params);
		    return ch;
		}
		// @__NO_SIDE_EFFECTS__
		function _check(fn, params) {
		    const ch = new checks.$ZodCheck({
		        check: "custom",
		        ...util.normalizeParams(params),
		    });
		    ch._zod.check = fn;
		    return ch;
		}
		// @__NO_SIDE_EFFECTS__
		function describe(description) {
		    const ch = new checks.$ZodCheck({ check: "describe" });
		    ch._zod.onattach = [
		        (inst) => {
		            const existing = registries.globalRegistry.get(inst) ?? {};
		            registries.globalRegistry.add(inst, { ...existing, description });
		        },
		    ];
		    ch._zod.check = () => { }; // no-op check
		    return ch;
		}
		// @__NO_SIDE_EFFECTS__
		function meta(metadata) {
		    const ch = new checks.$ZodCheck({ check: "meta" });
		    ch._zod.onattach = [
		        (inst) => {
		            const existing = registries.globalRegistry.get(inst) ?? {};
		            registries.globalRegistry.add(inst, { ...existing, ...metadata });
		        },
		    ];
		    ch._zod.check = () => { }; // no-op check
		    return ch;
		}
		// @__NO_SIDE_EFFECTS__
		function _stringbool(Classes, _params) {
		    const params = util.normalizeParams(_params);
		    let truthyArray = params.truthy ?? ["true", "1", "yes", "on", "y", "enabled"];
		    let falsyArray = params.falsy ?? ["false", "0", "no", "off", "n", "disabled"];
		    if (params.case !== "sensitive") {
		        truthyArray = truthyArray.map((v) => (typeof v === "string" ? v.toLowerCase() : v));
		        falsyArray = falsyArray.map((v) => (typeof v === "string" ? v.toLowerCase() : v));
		    }
		    const truthySet = new Set(truthyArray);
		    const falsySet = new Set(falsyArray);
		    const _Codec = Classes.Codec ?? schemas.$ZodCodec;
		    const _Boolean = Classes.Boolean ?? schemas.$ZodBoolean;
		    const _String = Classes.String ?? schemas.$ZodString;
		    const stringSchema = new _String({ type: "string", error: params.error });
		    const booleanSchema = new _Boolean({ type: "boolean", error: params.error });
		    const codec = new _Codec({
		        type: "pipe",
		        in: stringSchema,
		        out: booleanSchema,
		        transform: ((input, payload) => {
		            let data = input;
		            if (params.case !== "sensitive")
		                data = data.toLowerCase();
		            if (truthySet.has(data)) {
		                return true;
		            }
		            else if (falsySet.has(data)) {
		                return false;
		            }
		            else {
		                payload.issues.push({
		                    code: "invalid_value",
		                    expected: "stringbool",
		                    values: [...truthySet, ...falsySet],
		                    input: payload.value,
		                    inst: codec,
		                    continue: false,
		                });
		                return {};
		            }
		        }),
		        reverseTransform: ((input, _payload) => {
		            if (input === true) {
		                return truthyArray[0] || "true";
		            }
		            else {
		                return falsyArray[0] || "false";
		            }
		        }),
		        error: params.error,
		    });
		    codec._zod.bag.truthy = truthyArray;
		    codec._zod.bag.falsy = falsyArray;
		    codec._zod.bag.case = params.case ?? "insensitive";
		    return codec;
		}
		// @__NO_SIDE_EFFECTS__
		function _stringFormat(Class, format, fnOrRegex, _params = {}) {
		    const params = util.normalizeParams(_params);
		    const def = {
		        check: "string_format",
		        type: "string",
		        format,
		        fn: typeof fnOrRegex === "function" ? fnOrRegex : (val) => fnOrRegex.test(val),
		        ...params,
		    };
		    if (fnOrRegex instanceof RegExp) {
		        def.pattern = fnOrRegex;
		    }
		    const inst = new Class(def);
		    return inst;
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (api));
	return api;
}

var toJsonSchema = {};

var hasRequiredToJsonSchema;

function requireToJsonSchema () {
	if (hasRequiredToJsonSchema) return toJsonSchema;
	hasRequiredToJsonSchema = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.createStandardJSONSchemaMethod = exports.createToJSONSchemaMethod = void 0;
		exports.initializeContext = initializeContext;
		exports.handleUnrepresentable = handleUnrepresentable;
		exports.processSchema = processSchema;
		exports.process = processSchema;
		exports.processSchema = processSchema;
		exports.process = processSchema;
		exports.extractDefs = extractDefs;
		exports.finalize = finalize;
		const registries_js_1 = /*@__PURE__*/ requireRegistries();
		const util_js_1 = /*@__PURE__*/ requireUtil$2();
		function assignProps(target, ...sources) {
		    for (const source of sources) {
		        for (const key of Reflect.ownKeys(source)) {
		            if (Object.prototype.propertyIsEnumerable.call(source, key)) {
		                (0, util_js_1.assignProp)(target, key, source[key]);
		            }
		        }
		    }
		    return target;
		}
		// function initializeContext<T extends schemas.$ZodType>(inputs: JSONSchemaGeneratorParams<T>): ToJSONSchemaContext<T> {
		//   return {
		//     processor: inputs.processor,
		//     metadataRegistry: inputs.metadata ?? globalRegistry,
		//     target: inputs.target ?? "draft-2020-12",
		//     unrepresentable: inputs.unrepresentable ?? "throw",
		//   };
		// }
		function initializeContext(params) {
		    // Normalize target: convert old non-hyphenated versions to hyphenated versions
		    let target = params?.target ?? "draft-2020-12";
		    if (target === "draft-4")
		        target = "draft-04";
		    if (target === "draft-7")
		        target = "draft-07";
		    return {
		        processors: params.processors ?? {},
		        metadataRegistry: params?.metadata ?? registries_js_1.globalRegistry,
		        target,
		        unrepresentable: params?.unrepresentable ?? "throw",
		        override: params?.override ?? (() => { }),
		        io: params?.io ?? "output",
		        counter: 0,
		        seen: new Map(),
		        sharedDefsExtractedFor: undefined,
		        sharedEmitDoneFor: undefined,
		        cycles: params?.cycles ?? "ref",
		        reused: params?.reused ?? "inline",
		        intersections: [],
		        deferred: [],
		        external: params?.external ?? undefined,
		    };
		}
		/**
		 * Applies the `unrepresentable` setting at a site that has no JSON Schema equivalent. Throws
		 * `message` unless the setting (or the handler's return value) says otherwise. Returns `true` if a
		 * custom JSON Schema was written into `json`, in which case the caller must not write its own.
		 */
		function handleUnrepresentable(schema, ctx, json, params, message) {
		    const result = typeof ctx.unrepresentable === "function"
		        ? ctx.unrepresentable({ zodSchema: schema, path: params.path, message })
		        : ctx.unrepresentable;
		    if (result === "any")
		        return false;
		    if (result === undefined || result === "throw")
		        throw new Error(message);
		    Object.assign(json, result);
		    return true;
		}
		// never rename this back to `process`: bundler polyfills inject a top-level `const process` that a lexical declaration of the same name collides with (#6397)
		function processSchema(schema, ctx, _params = { path: [], schemaPath: [] }) {
		    var _a;
		    const def = schema._zod.def;
		    // check for schema in seens
		    const seen = ctx.seen.get(schema);
		    if (seen) {
		        seen.count++;
		        // check if cycle
		        const isCycle = _params.schemaPath.includes(schema);
		        if (isCycle) {
		            seen.cycle = _params.path;
		        }
		        return seen.schema;
		    }
		    // initialize
		    const result = { schema: {}, count: 1, cycle: undefined, path: _params.path };
		    ctx.seen.set(schema, result);
		    ctx.sharedDefsExtractedFor = undefined;
		    ctx.sharedEmitDoneFor = undefined;
		    // custom method overrides default behavior
		    const overrideSchema = schema._zod.toJSONSchema?.();
		    if (overrideSchema) {
		        result.schema = overrideSchema;
		    }
		    else {
		        const params = {
		            ..._params,
		            schemaPath: [..._params.schemaPath, schema],
		            path: _params.path,
		        };
		        if (schema._zod.processJSONSchema) {
		            schema._zod.processJSONSchema(ctx, result.schema, params);
		        }
		        else {
		            const _json = result.schema;
		            const processor = ctx.processors[def.type];
		            if (!processor) {
		                throw new Error(`[toJSONSchema]: Non-representable type encountered: ${def.type}`);
		            }
		            processor(schema, ctx, _json, params);
		        }
		        const parent = schema._zod.parent;
		        if (parent) {
		            // Also set ref if processor didn't (for inheritance)
		            if (!result.ref)
		                result.ref = parent;
		            processSchema(parent, ctx, params);
		            ctx.seen.get(parent).isParent = true;
		        }
		    }
		    // metadata
		    const meta = ctx.metadataRegistry.get(schema);
		    if (meta)
		        assignProps(result.schema, meta);
		    if (ctx.io === "input" && isTransforming(schema)) {
		        // examples/defaults only apply to output type of pipe
		        delete result.schema.examples;
		        delete result.schema.default;
		    }
		    // set prefault as default
		    if (ctx.io === "input" && "_prefault" in result.schema)
		        (_a = result.schema).default ?? (_a.default = result.schema._prefault);
		    delete result.schema._prefault;
		    // pulling fresh from ctx.seen in case it was overwritten
		    const _result = ctx.seen.get(schema);
		    return _result.schema;
		}
		// Escape a reference token for use in a JSON Pointer fragment (RFC 6901): `~` becomes `~0` and `/` becomes `~1`. The `~` replacement must run first.
		function encodeJSONPointerSegment(segment) {
		    return segment.replace(/~/g, "~0").replace(/\//g, "~1");
		}
		function extractDefs(ctx, schema
		// params: EmitParams
		) {
		    // iterate over seen map;
		    const root = ctx.seen.get(schema);
		    if (!root)
		        throw new Error("Unprocessed schema. This is a bug in Zod.");
		    // With `external` set, every registered schema resolves through the external branch of `makeURI`, so the root branch below produces the same ref the external branch would — this pass is identical whichever schema it is called with, and only needs to run once.
		    if (ctx.external && ctx.sharedDefsExtractedFor === ctx.external)
		        return;
		    // Track ids to detect duplicates across different schemas
		    const idToSchema = new Map();
		    for (const entry of ctx.seen.entries()) {
		        const id = ctx.metadataRegistry.get(entry[0])?.id;
		        if (id) {
		            const existing = idToSchema.get(id);
		            if (existing && existing !== entry[0]) {
		                throw new Error(`Duplicate schema id "${id}" detected during JSON Schema conversion. Two different schemas cannot share the same id when converted together.`);
		            }
		            idToSchema.set(id, entry[0]);
		        }
		    }
		    // returns a ref to the schema defId will be empty if the ref points to an external schema (or #)
		    const makeURI = (entry) => {
		        // comparing the seen objects because sometimes multiple schemas map to the same seen object. e.g. lazy
		        // external is configured
		        const defsSegment = ctx.target === "draft-2020-12" ? "$defs" : "definitions";
		        if (ctx.external) {
		            const externalId = ctx.external.registry.get(entry[0])?.id; // ?? "__shared";// `__schema${ctx.counter++}`;
		            // check if schema is in the external registry
		            const uriGenerator = ctx.external.uri ?? ((id) => id);
		            if (externalId) {
		                return { ref: uriGenerator(externalId) };
		            }
		            // otherwise, add to __shared
		            const id = entry[1].defId ?? entry[1].schema.id ?? `schema${ctx.counter++}`;
		            entry[1].defId = id; // set defId so it will be reused if needed
		            return { defId: id, ref: `${uriGenerator("__shared")}#/${defsSegment}/${encodeJSONPointerSegment(id)}` };
		        }
		        const uriPrefix = `#`;
		        const defUriPrefix = `${uriPrefix}/${defsSegment}/`;
		        // an id-less root has nowhere to be extracted to, so it stays inline and self-references as `#`
		        if (entry[1] === root && !entry[1].schema.id) {
		            return { ref: uriPrefix };
		        }
		        // self-contained schema
		        const defId = entry[1].schema.id ?? `__schema${ctx.counter++}`;
		        return { defId, ref: defUriPrefix + encodeJSONPointerSegment(defId) };
		    };
		    // stored cached version in `def` property remove all properties, set $ref
		    const extractToDef = (entry) => {
		        // if the schema is already a reference, do not extract it
		        if (entry[1].schema.$ref) {
		            return;
		        }
		        const seen = entry[1];
		        const { ref, defId } = makeURI(entry);
		        seen.def = { ...seen.schema };
		        // defId won't be set if the schema is a reference to an external schema or if the schema is the root schema
		        if (defId)
		            seen.defId = defId;
		        // wipe away all properties except $ref
		        const schema = seen.schema;
		        for (const key in schema) {
		            delete schema[key];
		        }
		        schema.$ref = ref;
		    };
		    // throw on cycles
		    // break cycles
		    if (ctx.cycles === "throw") {
		        for (const entry of ctx.seen.entries()) {
		            const seen = entry[1];
		            if (seen.cycle) {
		                throw new Error("Cycle detected: " +
		                    `#/${seen.cycle?.join("/")}/<root>` +
		                    '\n\nSet the `cycles` parameter to `"ref"` to resolve cyclical schemas with defs.');
		            }
		        }
		    }
		    // extract schemas into $defs
		    for (const entry of ctx.seen.entries()) {
		        const seen = entry[1];
		        // convert root schema to # $ref
		        if (schema === entry[0]) {
		            extractToDef(entry); // this has special handling for the root schema
		            continue;
		        }
		        // extract schemas that are in the external registry
		        if (ctx.external) {
		            const ext = ctx.external.registry.get(entry[0])?.id;
		            if (schema !== entry[0] && ext) {
		                extractToDef(entry);
		                continue;
		            }
		        }
		        // extract schemas with `id` meta
		        const id = ctx.metadataRegistry.get(entry[0])?.id;
		        if (id) {
		            extractToDef(entry);
		            continue;
		        }
		        // break cycles
		        if (seen.cycle) {
		            // any
		            extractToDef(entry);
		            continue;
		        }
		        // extract reused schemas
		        if (seen.count > 1) {
		            if (ctx.reused === "ref") {
		                extractToDef(entry);
		            }
		        }
		    }
		    if (ctx.external)
		        ctx.sharedDefsExtractedFor = ctx.external;
		}
		/** Rewrites `anyOf: [{type: "a"}, {type: "b"}]` to `type: ["a", "b"]`, which every JSON Schema draft treats as equivalent and most consumers render far better for the nullable case. Only branches that are a bare type assertion qualify — anything carrying a constraint, `$ref`, `const` or metadata is left alone. Runs after `flattenRef`, so a branch an override decorated or `$defs` extraction turned into a `$ref` is no longer bare and correctly stays in `anyOf`. `oneOf` is excluded: `integer` and `number` overlap, so "exactly one" and "at least one" are not the same there. OpenAPI 3.0 is excluded: its `type` must be a single string. */
		function compactTypeUnion(schema) {
		    const options = schema.anyOf;
		    if (!Array.isArray(options) || options.length === 0 || schema.type !== undefined)
		        return;
		    const types = [];
		    for (const option of options) {
		        if (!option || typeof option !== "object")
		            return;
		        // A branch that is itself a compactible union folds into this one — nested `anyOf` and a flat `type` array say the same thing. Compacting it first also makes the result independent of the order this pass walks the seen map in.
		        compactTypeUnion(option);
		        const keys = Object.keys(option);
		        if (keys.length !== 1 || keys[0] !== "type")
		            return;
		        const type = option.type;
		        for (const member of Array.isArray(type) ? type : [type]) {
		            if (typeof member !== "string")
		                return;
		            if (!types.includes(member))
		                types.push(member);
		        }
		    }
		    delete schema.anyOf;
		    // A `type` array must be non-empty and unique (metaschema); a single member is spelled as a bare string.
		    schema.type = types.length === 1 ? types[0] : types;
		}
		/** Keywords `foldIntersection` knows how to combine. Anything else — `$ref`, `patternProperties`,
		 * an annotation like `description` — makes a member unfoldable, so a constraint this does not
		 * understand leaves the `allOf` alone instead of being silently dropped or misattributed. */
		const FOLDABLE_KEYS = new Set(["type", "properties", "required", "additionalProperties"]);
		const UNION_KEYS = ["oneOf", "anyOf"];
		/** A member's constraint on a key it does not declare itself. A `catchall` states one; `false`, an absent `additionalProperties`, and the empty schema a loose object emits state nothing. */
		function undeclaredConstraint(member) {
		    const extra = member.additionalProperties;
		    if (extra === undefined || extra === false || typeof extra !== "object" || extra === null)
		        return null;
		    return Object.keys(extra).length ? extra : null;
		}
		/** Combines object members into the single object they describe together, or returns `null` if any of them carries a keyword outside {@link FOLDABLE_KEYS}. */
		function foldObjects(members) {
		    const objects = [];
		    for (const member of members) {
		        // A boolean subschema is legal JSON Schema and carries no keywords to fold.
		        if (typeof member !== "object" || member.type !== "object")
		            return null;
		        for (const key in member) {
		            if (!FOLDABLE_KEYS.has(key))
		                return null;
		        }
		        objects.push(member);
		    }
		    const properties = {};
		    const required = new Set();
		    for (const object of objects) {
		        for (const key in object.properties) {
		            // `in` would report a `__proto__` key as already present via the prototype chain and skip it.
		            if (Object.prototype.hasOwnProperty.call(properties, key))
		                continue;
		            // Every member constrains this key: the ones that declare it say how, and a `catchall` member constrains it too even though it does not name it. The key has to satisfy all of them, which is the same intersection one level down.
		            const parts = [];
		            for (const other of objects) {
		                const part = other.properties?.[key] ?? undeclaredConstraint(other);
		                if (part === null || part === undefined)
		                    continue;
		                if (!parts.some((seen) => JSON.stringify(seen) === JSON.stringify(part)))
		                    parts.push(part);
		            }
		            const merged = parts.length === 1
		                ? parts[0]
		                : (foldObjects(parts) ?? { allOf: parts });
		            (0, util_js_1.assignProp)(properties, key, merged);
		        }
		        for (const key of object.required ?? [])
		            required.add(key);
		    }
		    const folded = { type: "object", properties };
		    if (required.size)
		        folded.required = [...required];
		    // A key no member declares is rejected only when every member rejects it, so the fold is closed only when every member is. Otherwise it carries whatever the `catchall` members demand of such a key.
		    if (objects.every((object) => object.additionalProperties === false)) {
		        folded.additionalProperties = false;
		    }
		    else {
		        const constraints = [];
		        for (const object of objects) {
		            const constraint = undeclaredConstraint(object);
		            if (constraint && !constraints.some((seen) => JSON.stringify(seen) === JSON.stringify(constraint)))
		                constraints.push(constraint);
		        }
		        if (constraints.length === 1)
		            folded.additionalProperties = constraints[0];
		        else if (constraints.length > 1)
		            folded.additionalProperties = { allOf: constraints };
		    }
		    return folded;
		}
		/** `additionalProperties` in an `allOf` member sees only that member's own `properties`, so two
		 * closed object members reject each other's keys and the schema validates nothing. Zod's parser
		 * pools the key sets instead — `handleIntersectionResults` reports a key as unrecognized only when
		 * *every* side rejects it — so the emitted schema has to pool them too, and folding the members
		 * into one object is the encoding that says so on every target.
		 *
		 * This runs from `finalize`, after `extractDefs`, which is what keeps it clear of the `$ref`
		 * machinery: a member extracted into `$defs` is already a `$ref` by now and declines to fold, so it
		 * keeps its reference and its own closedness rather than being inlined as a stale copy. */
		function foldIntersection(json) {
		    const allOf = json.allOf;
		    if (!Array.isArray(allOf) || allOf.length < 2)
		        return;
		    // An `override` runs before this pass and may have written object keywords onto the intersection itself. Those are deliberate, so decline rather than overwrite them.
		    for (const key of FOLDABLE_KEYS)
		        if (key in json)
		            return;
		    // An intersection distributes over a union: `A & (X | Y)` is `(A & X) | (A & Y)`. Only the first union is distributed over; a second one stays among the members every branch folds against, where it fails the object check and declines the whole intersection rather than multiplying out.
		    const unions = allOf.filter((m) => UNION_KEYS.some((k) => Array.isArray(m[k])));
		    let folded = null;
		    if (!unions.length) {
		        folded = foldObjects(allOf);
		    }
		    else {
		        const union = unions[0];
		        const keyword = UNION_KEYS.find((k) => Array.isArray(union[k]));
		        if (Object.keys(union).length !== 1)
		            return;
		        const rest = allOf.filter((m) => m !== union);
		        const branches = union[keyword].map((branch) => foldObjects([...rest, branch]));
		        if (branches.some((b) => !b))
		            return;
		        folded = { [keyword]: branches };
		    }
		    if (!folded)
		        return;
		    delete json.allOf;
		    assignProps(json, folded);
		}
		function finalize(ctx, schema) {
		    const root = ctx.seen.get(schema);
		    if (!root)
		        throw new Error("Unprocessed schema. This is a bug in Zod.");
		    // flatten refs - inherit properties from parent schemas
		    const flattenRef = (zodSchema) => {
		        const seen = ctx.seen.get(zodSchema);
		        // already processed
		        if (seen.ref === null)
		            return;
		        const schema = seen.def ?? seen.schema;
		        const _cached = { ...schema };
		        const ref = seen.ref;
		        seen.ref = null; // prevent infinite recursion
		        if (ref) {
		            flattenRef(ref);
		            const refSeen = ctx.seen.get(ref);
		            const refSchema = refSeen.schema;
		            // merge referenced schema into current
		            if (refSchema.$ref && (ctx.target === "draft-07" || ctx.target === "draft-04" || ctx.target === "openapi-3.0")) {
		                // older drafts can't combine $ref with other properties
		                schema.allOf = schema.allOf ?? [];
		                schema.allOf.push(refSchema);
		            }
		            else {
		                assignProps(schema, refSchema);
		            }
		            // restore child's own properties (child wins)
		            assignProps(schema, _cached);
		            const isParentRef = zodSchema._zod.parent === ref;
		            // For parent chain, child is a refinement - remove parent-only properties
		            if (isParentRef) {
		                for (const key in schema) {
		                    if (key === "$ref" || key === "allOf")
		                        continue;
		                    if (!(key in _cached)) {
		                        delete schema[key];
		                    }
		                }
		            }
		            // When ref was extracted to $defs, remove properties that match the definition
		            if (refSchema.$ref && refSeen.def) {
		                for (const key in schema) {
		                    if (key === "$ref" || key === "allOf")
		                        continue;
		                    if (key in refSeen.def && JSON.stringify(schema[key]) === JSON.stringify(refSeen.def[key])) {
		                        delete schema[key];
		                    }
		                }
		            }
		        }
		        // If parent was extracted (has $ref), propagate $ref to this schema. This handles cases like: readonly().meta({id}).describe() where processor sets ref to innerType but parent should be referenced
		        const parent = zodSchema._zod.parent;
		        if (parent && parent !== ref) {
		            // Ensure parent is processed first so its def has inherited properties
		            flattenRef(parent);
		            const parentSeen = ctx.seen.get(parent);
		            if (parentSeen?.schema.$ref) {
		                schema.$ref = parentSeen.schema.$ref;
		                // De-duplicate with parent's definition
		                if (parentSeen.def) {
		                    for (const key in schema) {
		                        if (key === "$ref" || key === "allOf")
		                            continue;
		                        if (key in parentSeen.def && JSON.stringify(schema[key]) === JSON.stringify(parentSeen.def[key])) {
		                            delete schema[key];
		                        }
		                    }
		                }
		            }
		        }
		        // execute overrides
		        ctx.override({
		            zodSchema: zodSchema,
		            jsonSchema: schema,
		            path: seen.path ?? [],
		        });
		    };
		    // Flattening walks the whole map and clears each `ref` as it goes, so a second call over the same map is a no-op scan. Skip it outright once it has run for a registry conversion.
		    if (!ctx.external || ctx.sharedEmitDoneFor !== ctx.external) {
		        for (const entry of [...ctx.seen.entries()].reverse()) {
		            flattenRef(entry[0]);
		        }
		        if (ctx.target !== "openapi-3.0") {
		            for (const entry of ctx.seen.entries()) {
		                compactTypeUnion(entry[1].def ?? entry[1].schema);
		            }
		        }
		        for (const rewrite of ctx.deferred)
		            rewrite();
		        // After flattening, every member that was extracted is a `$ref`, so the fold sees the final shape. A schema that inherits an intersection — through `z.lazy`, or any `ref` chain — holds the same `allOf` array, so fold by array identity to catch every copy.
		        if (ctx.intersections.length) {
		            const carriers = new Map();
		            for (const seen of ctx.seen.values()) {
		                for (const json of [seen.schema, seen.def]) {
		                    const allOf = json?.allOf;
		                    if (!Array.isArray(allOf))
		                        continue;
		                    const existing = carriers.get(allOf);
		                    if (existing)
		                        existing.push(json);
		                    else
		                        carriers.set(allOf, [json]);
		                }
		            }
		            for (const allOf of ctx.intersections) {
		                for (const json of carriers.get(allOf) ?? [])
		                    foldIntersection(json);
		            }
		        }
		    }
		    const result = {};
		    if (ctx.target === "draft-2020-12") {
		        result.$schema = "https://json-schema.org/draft/2020-12/schema";
		    }
		    else if (ctx.target === "draft-07") {
		        result.$schema = "http://json-schema.org/draft-07/schema#";
		    }
		    else if (ctx.target === "draft-04") {
		        result.$schema = "http://json-schema.org/draft-04/schema#";
		    }
		    else if (ctx.target === "openapi-3.0") ;
		    else ;
		    if (ctx.external?.uri) {
		        const id = ctx.external.registry.get(schema)?.id;
		        if (!id)
		            throw new Error("Schema is missing an `id` property");
		        result.$id = ctx.external.uri(id);
		    }
		    // when the root was extracted into $defs, `root.schema` is the `$ref` wrapper and `root.def` is the body that now lives under $defs
		    assignProps(result, root.defId ? root.schema : (root.def ?? root.schema));
		    // The `id` in `.meta()` is a Zod-specific registration tag used to extract schemas into $defs — it is not user-facing JSON Schema metadata. Strip it from the output body where it would otherwise leak. The id is preserved implicitly via the $defs key (and via $ref paths).
		    const rootMetaId = ctx.metadataRegistry.get(schema)?.id;
		    if (rootMetaId !== undefined && result.id === rootMetaId)
		        delete result.id;
		    // build defs object. With `external`, `defs` is the shared object every schema writes into, so the same entries are reassigned on every call. Without it, `defs` is fresh per call and must be rebuilt.
		    const defs = ctx.external?.defs ?? {};
		    if (!ctx.external || ctx.sharedEmitDoneFor !== ctx.external) {
		        for (const entry of ctx.seen.entries()) {
		            const seen = entry[1];
		            if (seen.def && seen.defId) {
		                if (seen.def.id === seen.defId)
		                    delete seen.def.id;
		                (0, util_js_1.assignProp)(defs, seen.defId, seen.def);
		            }
		        }
		    }
		    if (ctx.external)
		        ctx.sharedEmitDoneFor = ctx.external;
		    // set definitions in result
		    if (ctx.external) ;
		    else {
		        if (Object.keys(defs).length > 0) {
		            if (ctx.target === "draft-2020-12") {
		                result.$defs = defs;
		            }
		            else {
		                result.definitions = defs;
		            }
		        }
		    }
		    try {
		        // this "finalizes" this schema and ensures all cycles are removed each call to finalize() is functionally independent though the seen map is shared
		        const finalized = JSON.parse(JSON.stringify(result));
		        Object.defineProperty(finalized, "~standard", {
		            value: {
		                ...schema["~standard"],
		                jsonSchema: {
		                    input: (0, exports.createStandardJSONSchemaMethod)(schema, "input", ctx.processors),
		                    output: (0, exports.createStandardJSONSchemaMethod)(schema, "output", ctx.processors),
		                },
		            },
		            enumerable: false,
		            writable: false,
		        });
		        return finalized;
		    }
		    catch (_err) {
		        throw new Error("Error converting schema to JSON.");
		    }
		}
		function isTransforming(_schema, _ctx) {
		    const ctx = _ctx ?? { seen: new Set() };
		    if (ctx.seen.has(_schema))
		        return false;
		    ctx.seen.add(_schema);
		    const def = _schema._zod.def;
		    if (def.type === "transform")
		        return true;
		    if (def.type === "array")
		        return isTransforming(def.element, ctx);
		    if (def.type === "set")
		        return isTransforming(def.valueType, ctx);
		    if (def.type === "lazy")
		        return isTransforming(def.getter(), ctx);
		    if (def.type === "promise" ||
		        def.type === "optional" ||
		        def.type === "nonoptional" ||
		        def.type === "nullable" ||
		        def.type === "readonly" ||
		        def.type === "default" ||
		        def.type === "prefault" ||
		        def.type === "catch") {
		        return isTransforming(def.innerType, ctx);
		    }
		    if (def.type === "intersection") {
		        return isTransforming(def.left, ctx) || isTransforming(def.right, ctx);
		    }
		    if (def.type === "record" || def.type === "map") {
		        return isTransforming(def.keyType, ctx) || isTransforming(def.valueType, ctx);
		    }
		    if (def.type === "pipe") {
		        if (_schema._zod.traits.has("$ZodCodec"))
		            return true;
		        return isTransforming(def.in, ctx) || isTransforming(def.out, ctx);
		    }
		    if (def.type === "object") {
		        for (const key in def.shape) {
		            if (isTransforming(def.shape[key], ctx))
		                return true;
		        }
		        return false;
		    }
		    if (def.type === "union") {
		        for (const option of def.options) {
		            if (isTransforming(option, ctx))
		                return true;
		        }
		        return false;
		    }
		    if (def.type === "tuple") {
		        for (const item of def.items) {
		            if (isTransforming(item, ctx))
		                return true;
		        }
		        if (def.rest && isTransforming(def.rest, ctx))
		            return true;
		        return false;
		    }
		    return false;
		}
		/**
		 * Creates a toJSONSchema method for a schema instance.
		 * This encapsulates the logic of initializing context, processing, extracting defs, and finalizing.
		 */
		const createToJSONSchemaMethod = (schema, processors = {}) => (params) => {
		    const ctx = initializeContext({ ...params, processors });
		    processSchema(schema, ctx);
		    extractDefs(ctx, schema);
		    return finalize(ctx, schema);
		};
		exports.createToJSONSchemaMethod = createToJSONSchemaMethod;
		const createStandardJSONSchemaMethod = (schema, io, processors = {}) => (params) => {
		    const { libraryOptions, target } = params ?? {};
		    const ctx = initializeContext({ ...(libraryOptions ?? {}), target, io, processors });
		    processSchema(schema, ctx);
		    extractDefs(ctx, schema);
		    return finalize(ctx, schema);
		};
		exports.createStandardJSONSchemaMethod = createStandardJSONSchemaMethod;

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (toJsonSchema));
	return toJsonSchema;
}

var jsonSchemaProcessors = {};

var hasRequiredJsonSchemaProcessors;

function requireJsonSchemaProcessors () {
	if (hasRequiredJsonSchemaProcessors) return jsonSchemaProcessors;
	hasRequiredJsonSchemaProcessors = 1;
	(function (exports) {
		var __createBinding = (jsonSchemaProcessors && jsonSchemaProcessors.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (jsonSchemaProcessors && jsonSchemaProcessors.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (jsonSchemaProcessors && jsonSchemaProcessors.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.allProcessors = exports.lazyProcessor = exports.optionalProcessor = exports.promiseProcessor = exports.readonlyProcessor = exports.pipeProcessor = exports.catchProcessor = exports.prefaultProcessor = exports.defaultProcessor = exports.nonoptionalProcessor = exports.nullableProcessor = exports.recordProcessor = exports.tupleProcessor = exports.intersectionProcessor = exports.unionProcessor = exports.objectProcessor = exports.arrayProcessor = exports.setProcessor = exports.mapProcessor = exports.transformProcessor = exports.functionProcessor = exports.customProcessor = exports.successProcessor = exports.fileProcessor = exports.templateLiteralProcessor = exports.nanProcessor = exports.literalProcessor = exports.enumProcessor = exports.dateProcessor = exports.unknownProcessor = exports.anyProcessor = exports.neverProcessor = exports.voidProcessor = exports.undefinedProcessor = exports.nullProcessor = exports.symbolProcessor = exports.bigintProcessor = exports.booleanProcessor = exports.numberProcessor = exports.stringProcessor = void 0;
		exports.aggregateChecks = aggregateChecks;
		exports.toJSONSchema = toJSONSchema;
		const regexes = __importStar(/*@__PURE__*/ requireRegexes());
		const schemas_js_1 = /*@__PURE__*/ requireSchemas$1();
		const to_json_schema_js_1 = /*@__PURE__*/ requireToJsonSchema();
		const util_js_1 = /*@__PURE__*/ requireUtil$2();
		const narrowMin = (agg, key, value) => {
		    if (agg[key] === undefined || value > agg[key])
		        agg[key] = value;
		};
		const narrowMax = (agg, key, value) => {
		    if (agg[key] === undefined || value < agg[key])
		        agg[key] = value;
		};
		const narrowBoth = (agg, value) => {
		    narrowMin(agg, "minimum", value);
		    narrowMax(agg, "maximum", value);
		};
		const addDivisor = (agg, value) => {
		    agg.multipleOf ?? (agg.multipleOf = []);
		    if (!agg.multipleOf.includes(value))
		        agg.multipleOf.push(value);
		};
		const addPattern = (agg, pattern) => {
		    agg.patterns ?? (agg.patterns = new Set());
		    agg.patterns.add(pattern);
		};
		const intersectMime = (agg, mime) => {
		    agg.mime = agg.mime ? agg.mime.filter((m) => mime.includes(m)) : [...mime];
		};
		// last-wins, matching the bag's historical write order; the flag keeps an integer format from being lost to a later float one
		const setFormat = (agg, format) => {
		    agg.format = format;
		    if (format.includes("int"))
		        agg.isInt = true;
		};
		const minContributor = (agg, def) => narrowMin(agg, "minimum", def.minimum);
		const maxContributor = (agg, def) => narrowMax(agg, "maximum", def.maximum);
		const formatContributor = (ranges) => (agg, def) => {
		    setFormat(agg, def.format);
		    const [minimum, maximum] = ranges[def.format];
		    narrowMin(agg, "minimum", minimum);
		    narrowMax(agg, "maximum", maximum);
		};
		const contributors = {
		    greater_than: (agg, def) => narrowMin(agg, def.inclusive ? "minimum" : "exclusiveMinimum", def.value),
		    less_than: (agg, def) => narrowMax(agg, def.inclusive ? "maximum" : "exclusiveMaximum", def.value),
		    multiple_of: (agg, def) => addDivisor(agg, def.value),
		    number_format: formatContributor(util_js_1.NUMBER_FORMAT_RANGES),
		    bigint_format: formatContributor(util_js_1.BIGINT_FORMAT_RANGES),
		    min_length: minContributor,
		    max_length: maxContributor,
		    length_equals: (agg, def) => narrowBoth(agg, def.length),
		    min_size: minContributor,
		    max_size: maxContributor,
		    size_equals: (agg, def) => narrowBoth(agg, def.size),
		    string_format: (agg, def) => {
		        setFormat(agg, def.format);
		        if (def.pattern)
		            addPattern(agg, def.pattern);
		        if (def.format === "base64" || def.format === "base64url")
		            agg.contentEncoding = def.format;
		        if (def.local || def.precision === -1)
		            agg.laxFormat = true;
		    },
		    mime_type: (agg, def) => intersectMime(agg, def.mime),
		};
		function aggregateChecks(schema) {
		    const agg = {};
		    const def = schema._zod.def;
		    // a format schema is its own first check, same rule as $ZodType init
		    const list = schema._zod.traits.has("$ZodCheck")
		        ? [schema, ...(def.checks ?? [])]
		        : (def.checks ?? []);
		    for (const ch of list)
		        contributors[ch._zod.def.check]?.(agg, ch._zod.def);
		    // reconcile with the bag so third-party onattach contributions still land; first-party residue is never tighter than the fold, so merging it back is idempotent for one and additive for the other
		    const bag = schema._zod.bag;
		    if (bag.minimum !== undefined)
		        narrowMin(agg, "minimum", bag.minimum);
		    if (bag.exclusiveMinimum !== undefined)
		        narrowMin(agg, "exclusiveMinimum", bag.exclusiveMinimum);
		    if (bag.maximum !== undefined)
		        narrowMax(agg, "maximum", bag.maximum);
		    if (bag.exclusiveMaximum !== undefined)
		        narrowMax(agg, "exclusiveMaximum", bag.exclusiveMaximum);
		    if (bag.multipleOf !== undefined)
		        addDivisor(agg, bag.multipleOf);
		    if (bag.format !== undefined) {
		        agg.format ?? (agg.format = bag.format);
		        if (bag.format.includes("int"))
		            agg.isInt = true;
		    }
		    if (bag.mime)
		        intersectMime(agg, bag.mime);
		    for (const pattern of bag.patterns ?? [])
		        addPattern(agg, pattern);
		    return agg;
		}
		const formatMap = {
		    guid: "uuid",
		    url: "uri",
		    datetime: "date-time",
		    json_string: "json-string",
		    regex: "", // do not set
		};
		// ==================== SIMPLE TYPE PROCESSORS ====================
		// the runtime patterns are lax so parse paths never overflow the regex stack; the emitted schema swaps in the exact block forms, which zod itself never executes
		const exactPatterns = new Map([
		    [schemas_js_1.base64Charset, regexes.base64],
		    [schemas_js_1.base64urlCharset, regexes.base64url],
		]);
		const exactPattern = (p) => exactPatterns.get(p) ?? p;
		const stringProcessor = (schema, ctx, _json, _params) => {
		    const json = _json;
		    json.type = "string";
		    const { minimum, maximum, format, patterns, contentEncoding, laxFormat } = aggregateChecks(schema);
		    if (typeof minimum === "number")
		        json.minLength = minimum;
		    if (typeof maximum === "number")
		        json.maxLength = maximum;
		    // custom pattern overrides format
		    if (format) {
		        json.format = formatMap[format] ?? format;
		        if (json.format === "")
		            delete json.format; // empty format is not valid
		        // `z.iso.time()` is never full-time, and `laxFormat` carries the datetime shapes that also accept what their keyword forbids
		        if (format === "time" || laxFormat) {
		            delete json.format;
		        }
		    }
		    if (contentEncoding)
		        json.contentEncoding = contentEncoding;
		    if (patterns && patterns.size > 0) {
		        const patternList = [...patterns].map(exactPattern);
		        if (patternList.length === 1)
		            json.pattern = patternList[0].source;
		        else if (patternList.length > 1) {
		            json.allOf = [
		                ...patternList.map((regex) => ({
		                    ...(ctx.target === "draft-07" || ctx.target === "draft-04" || ctx.target === "openapi-3.0"
		                        ? { type: "string" }
		                        : {}),
		                    pattern: regex.source,
		                })),
		            ];
		        }
		    }
		};
		exports.stringProcessor = stringProcessor;
		const numberProcessor = (schema, ctx, _json, params) => {
		    const json = _json;
		    const { minimum, maximum, multipleOf, exclusiveMaximum, exclusiveMinimum, isInt } = aggregateChecks(schema);
		    json.type = isInt ? "integer" : "number";
		    // when both minimum and exclusiveMinimum exist, pick the more restrictive one
		    const exMin = typeof exclusiveMinimum === "number" && exclusiveMinimum >= (minimum ?? Number.NEGATIVE_INFINITY);
		    const exMax = typeof exclusiveMaximum === "number" && exclusiveMaximum <= (maximum ?? Number.POSITIVE_INFINITY);
		    const legacy = ctx.target === "draft-04" || ctx.target === "openapi-3.0";
		    if (exMin) {
		        if (legacy) {
		            json.minimum = exclusiveMinimum;
		            json.exclusiveMinimum = true;
		        }
		        else {
		            json.exclusiveMinimum = exclusiveMinimum;
		        }
		    }
		    else if (typeof minimum === "number") {
		        json.minimum = minimum;
		    }
		    if (exMax) {
		        if (legacy) {
		            json.maximum = exclusiveMaximum;
		            json.exclusiveMaximum = true;
		        }
		        else {
		            json.exclusiveMaximum = exclusiveMaximum;
		        }
		    }
		    else if (typeof maximum === "number") {
		        json.maximum = maximum;
		    }
		    if (multipleOf) {
		        // JSON Schema requires a divisor strictly greater than zero, and a non-finite one does not survive JSON at all. A negative divisor accepts exactly what its absolute value accepts, so it still maps; zero, NaN and Infinity have no keyword form.
		        const divisors = new Set();
		        for (const divisor of multipleOf) {
		            if (Number.isFinite(divisor) && divisor !== 0)
		                divisors.add(Math.abs(divisor));
		            else
		                (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, `A multipleOf divisor of ${divisor} cannot be represented in JSON Schema`);
		        }
		        // chained divisors are a conjunction the keyword cannot carry alone, so extras ride an allOf, same as stacked patterns
		        const [first, ...rest] = divisors;
		        if (first !== undefined)
		            json.multipleOf = first;
		        if (rest.length)
		            json.allOf = [...(json.allOf ?? []), ...rest.map((m) => ({ multipleOf: m }))];
		    }
		};
		exports.numberProcessor = numberProcessor;
		const booleanProcessor = (_schema, _ctx, json, _params) => {
		    json.type = "boolean";
		};
		exports.booleanProcessor = booleanProcessor;
		const bigintProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "BigInt cannot be represented in JSON Schema");
		};
		exports.bigintProcessor = bigintProcessor;
		const symbolProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Symbols cannot be represented in JSON Schema");
		};
		exports.symbolProcessor = symbolProcessor;
		const nullProcessor = (_schema, ctx, json, _params) => {
		    if (ctx.target === "openapi-3.0") {
		        json.type = "string";
		        json.nullable = true;
		        json.enum = [null];
		    }
		    else {
		        json.type = "null";
		    }
		};
		exports.nullProcessor = nullProcessor;
		const undefinedProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Undefined cannot be represented in JSON Schema");
		};
		exports.undefinedProcessor = undefinedProcessor;
		const voidProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Void cannot be represented in JSON Schema");
		};
		exports.voidProcessor = voidProcessor;
		const neverProcessor = (_schema, _ctx, json, _params) => {
		    json.not = {};
		};
		exports.neverProcessor = neverProcessor;
		const anyProcessor = (_schema, _ctx, _json, _params) => {
		    // empty schema accepts anything
		};
		exports.anyProcessor = anyProcessor;
		const unknownProcessor = (_schema, _ctx, _json, _params) => {
		    // empty schema accepts anything
		};
		exports.unknownProcessor = unknownProcessor;
		const dateProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Date cannot be represented in JSON Schema");
		};
		exports.dateProcessor = dateProcessor;
		const enumProcessor = (schema, _ctx, json, _params) => {
		    const def = schema._zod.def;
		    const values = (0, util_js_1.getEnumValues)(def.entries);
		    // an empty enum accepts nothing, same as z.never()
		    if (values.length === 0) {
		        json.not = {};
		        return;
		    }
		    // Number enums can have both string and number values
		    if (values.every((v) => typeof v === "number"))
		        json.type = "number";
		    if (values.every((v) => typeof v === "string"))
		        json.type = "string";
		    json.enum = values;
		};
		exports.enumProcessor = enumProcessor;
		const literalProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    // a literal with no values accepts nothing, same as z.never()
		    if (def.values.length === 0) {
		        json.not = {};
		        return;
		    }
		    const vals = [];
		    for (const val of def.values) {
		        if (val === undefined) {
		            // a custom schema replaces the whole literal, so there is nothing left to accumulate
		            if ((0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Literal `undefined` cannot be represented in JSON Schema"))
		                return;
		            // otherwise do not add to vals
		        }
		        else if (typeof val === "bigint") {
		            if ((0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "BigInt literals cannot be represented in JSON Schema"))
		                return;
		            vals.push(Number(val));
		        }
		        else {
		            vals.push(val);
		        }
		    }
		    if (vals.length === 0) ;
		    else if (vals.length === 1) {
		        const val = vals[0];
		        json.type = val === null ? "null" : typeof val;
		        if (ctx.target === "draft-04" || ctx.target === "openapi-3.0") {
		            json.enum = [val];
		        }
		        else {
		            json.const = val;
		        }
		    }
		    else {
		        if (vals.every((v) => typeof v === "number"))
		            json.type = "number";
		        if (vals.every((v) => typeof v === "string"))
		            json.type = "string";
		        if (vals.every((v) => typeof v === "boolean"))
		            json.type = "boolean";
		        if (vals.every((v) => v === null))
		            json.type = "null";
		        json.enum = vals;
		    }
		};
		exports.literalProcessor = literalProcessor;
		const nanProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "NaN cannot be represented in JSON Schema");
		};
		exports.nanProcessor = nanProcessor;
		const templateLiteralProcessor = (schema, _ctx, json, _params) => {
		    const _json = json;
		    const pattern = schema._zod.pattern;
		    if (!pattern)
		        throw new Error("Pattern not found in template literal");
		    _json.type = "string";
		    _json.pattern = pattern.source;
		};
		exports.templateLiteralProcessor = templateLiteralProcessor;
		const fileProcessor = (schema, _ctx, json, _params) => {
		    const _json = json;
		    _json.type = "string";
		    _json.format = "binary";
		    _json.contentEncoding = "binary";
		    const { minimum, maximum, mime } = aggregateChecks(schema);
		    if (minimum !== undefined)
		        _json.minLength = minimum;
		    if (maximum !== undefined)
		        _json.maxLength = maximum;
		    if (!mime)
		        return;
		    // an empty intersection means the mime checks share no value, so nothing passes at runtime; `anyOf` must be non-empty, so the false schema is `not: {}`
		    if (mime.length === 0)
		        _json.not = {};
		    else if (mime.length === 1)
		        _json.contentMediaType = mime[0];
		    // only contentMediaType differs, so the shared props stay at the root
		    else
		        _json.anyOf = mime.map((m) => ({ contentMediaType: m }));
		};
		exports.fileProcessor = fileProcessor;
		const successProcessor = (_schema, _ctx, json, _params) => {
		    json.type = "boolean";
		};
		exports.successProcessor = successProcessor;
		const customProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Custom types cannot be represented in JSON Schema");
		};
		exports.customProcessor = customProcessor;
		const functionProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Function types cannot be represented in JSON Schema");
		};
		exports.functionProcessor = functionProcessor;
		const transformProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Transforms cannot be represented in JSON Schema");
		};
		exports.transformProcessor = transformProcessor;
		const mapProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Map cannot be represented in JSON Schema");
		};
		exports.mapProcessor = mapProcessor;
		const setProcessor = (schema, ctx, json, params) => {
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Set cannot be represented in JSON Schema");
		};
		exports.setProcessor = setProcessor;
		// ==================== COMPOSITE TYPE PROCESSORS ====================
		const arrayProcessor = (schema, ctx, _json, params) => {
		    const json = _json;
		    const def = schema._zod.def;
		    const { minimum, maximum } = aggregateChecks(schema);
		    if (typeof minimum === "number")
		        json.minItems = minimum;
		    if (typeof maximum === "number")
		        json.maxItems = maximum;
		    json.type = "array";
		    json.items = (0, to_json_schema_js_1.processSchema)(def.element, ctx, {
		        ...params,
		        path: [...params.path, "items"],
		    });
		};
		exports.arrayProcessor = arrayProcessor;
		// Transform and catch set `optin = "optional"` at runtime so the parser lets them observe an
		// absent key, but their declared input type stays required. An input JSON Schema describes the
		// declared type, so resolve past them to the schema that actually carries the optionality.
		// Used by both `objectProcessor` (for `required`) and `tupleProcessor` (for `minItems`); see
		// wiki/optionality.md, "The JSON Schema emitter reads the *static* value".
		function inputOptin(schema) {
		    const def = schema._zod.def;
		    if (def.type === "pipe" && def.in._zod.traits.has("$ZodTransform")) {
		        return inputOptin(def.out);
		    }
		    if (def.type === "catch") {
		        return inputOptin(def.innerType);
		    }
		    return schema._zod.optin;
		}
		const objectProcessor = (schema, ctx, _json, params) => {
		    const json = _json;
		    const def = schema._zod.def;
		    const shape = def.shape;
		    // dropping it while still emitting `additionalProperties: false` would emit a schema that rejects data this one requires
		    const symbolKeys = Object.getOwnPropertySymbols(shape);
		    if (symbolKeys.length &&
		        (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Symbol keys cannot be represented in JSON Schema")) {
		        return;
		    }
		    json.type = "object";
		    json.properties = {};
		    for (const key in shape) {
		        // assignProp so a __proto__ key becomes an own property instead of hitting the inherited setter on the plain {} we build into
		        (0, util_js_1.assignProp)(json.properties, key, (0, to_json_schema_js_1.processSchema)(shape[key], ctx, {
		            ...params,
		            path: [...params.path, "properties", key],
		        }));
		    }
		    // required keys
		    const requiredKeys = [];
		    for (const key of Object.keys(shape)) {
		        const field = def.shape[key];
		        if (ctx.io === "input" ? inputOptin(field) === undefined : field._zod.optout === undefined) {
		            requiredKeys.push(key);
		        }
		    }
		    if (requiredKeys.length > 0) {
		        json.required = requiredKeys;
		    }
		    // catchall
		    if (def.catchall?._zod.def.type === "never") {
		        // strict
		        json.additionalProperties = false;
		    }
		    else if (!def.catchall) {
		        // regular
		        if (ctx.io === "output")
		            json.additionalProperties = false;
		    }
		    else if (def.catchall) {
		        json.additionalProperties = (0, to_json_schema_js_1.processSchema)(def.catchall, ctx, {
		            ...params,
		            path: [...params.path, "additionalProperties"],
		        });
		    }
		};
		exports.objectProcessor = objectProcessor;
		const unionProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    // Exclusive unions (inclusive === false) use oneOf (exactly one match) instead of anyOf (one or more matches). This includes both z.xor() and discriminated unions
		    const isExclusive = def.inclusive === false;
		    const options = def.options.map((x, i) => (0, to_json_schema_js_1.processSchema)(x, ctx, {
		        ...params,
		        path: [...params.path, isExclusive ? "oneOf" : "anyOf", i],
		    }));
		    if (isExclusive) {
		        json.oneOf = options;
		    }
		    else {
		        json.anyOf = options;
		    }
		};
		exports.unionProcessor = unionProcessor;
		const intersectionProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    const a = (0, to_json_schema_js_1.processSchema)(def.left, ctx, {
		        ...params,
		        path: [...params.path, "allOf", 0],
		    });
		    const b = (0, to_json_schema_js_1.processSchema)(def.right, ctx, {
		        ...params,
		        path: [...params.path, "allOf", 1],
		    });
		    const isSimpleIntersection = (val) => "allOf" in val && Object.keys(val).length === 1;
		    const allOf = [
		        ...(isSimpleIntersection(a) ? a.allOf : [a]),
		        ...(isSimpleIntersection(b) ? b.allOf : [b]),
		    ];
		    json.allOf = allOf;
		    // Recorded innermost first, so a nested intersection has already folded by the time this one is considered. The array is the handle rather than the schema, because a wrapper that inherits this schema shares the same array; `finalize` folds every object holding it. See `foldIntersection`.
		    ctx.intersections.push(allOf);
		};
		exports.intersectionProcessor = intersectionProcessor;
		const tupleProcessor = (schema, ctx, _json, params) => {
		    const json = _json;
		    const def = schema._zod.def;
		    json.type = "array";
		    const prefixPath = ctx.target === "draft-2020-12" ? "prefixItems" : "items";
		    const restPath = ctx.target === "draft-2020-12" ? "items" : ctx.target === "openapi-3.0" ? "items" : "additionalItems";
		    const prefixItems = def.items.map((x, i) => (0, to_json_schema_js_1.processSchema)(x, ctx, {
		        ...params,
		        path: [...params.path, prefixPath, i],
		    }));
		    const rest = def.rest
		        ? (0, to_json_schema_js_1.processSchema)(def.rest, ctx, {
		            ...params,
		            path: [...params.path, restPath, ...(ctx.target === "openapi-3.0" ? [def.items.length] : [])],
		        })
		        : null;
		    let minItems = def.items.length;
		    while (minItems > 0) {
		        const item = def.items[minItems - 1];
		        const optional = ctx.io === "input" ? inputOptin(item) !== undefined : item._zod.optout === "optional";
		        if (!optional)
		            break;
		        minItems--;
		    }
		    const maxItems = def.items.length;
		    const isClosed = !def.rest;
		    if (ctx.target === "draft-2020-12") {
		        json.prefixItems = prefixItems;
		        if (isClosed) {
		            json.items = false;
		        }
		        else if (rest) {
		            json.items = rest;
		        }
		        if (minItems > 0)
		            json.minItems = minItems;
		        if (isClosed)
		            json.maxItems = maxItems;
		    }
		    else if (ctx.target === "openapi-3.0") {
		        json.items = {
		            anyOf: prefixItems,
		        };
		        if (rest) {
		            json.items.anyOf.push(rest);
		        }
		        if (minItems > 0)
		            json.minItems = minItems;
		        if (isClosed)
		            json.maxItems = maxItems;
		    }
		    else {
		        json.items = prefixItems;
		        if (isClosed) {
		            json.additionalItems = false;
		        }
		        else if (rest) {
		            json.additionalItems = rest;
		        }
		        if (minItems > 0)
		            json.minItems = minItems;
		        if (isClosed)
		            json.maxItems = maxItems;
		    }
		    // explicit user-defined length checks take precedence
		    const { minimum, maximum } = aggregateChecks(schema);
		    if (typeof minimum === "number")
		        json.minItems = minimum;
		    if (typeof maximum === "number")
		        json.maxItems = maximum;
		};
		exports.tupleProcessor = tupleProcessor;
		/** JSON object keys are always strings, so a numeric record key schema is re-expressed over the
		 * numeric-string form the record parser matches. Deferred to `finalize`, after the flatten: a key
		 * behind a wrapper only carries its own `type` before then, and a union key only has its branches.
		 *
		 * A numeric bound cannot apply to a property name, so `minimum` and its siblings are dropped rather
		 * than carried over: keeping them beside `type: "string"` reproduces the match-nothing schema this
		 * exists to fix. A key that carries one therefore emits wider than the record parses — `z.record(z.number().min(5), V)`
		 * accepts `"3"` — which is the deliberate trade, since throwing on it would reject an ordinary schema
		 * outright. */
		function stringifyKeyNames(bySchema, json, visited) {
		    // an extracted key that rewrites cannot go on sharing its definition — the string form a key position needs is not the number form every other reference wants — so it inlines. One that does not rewrite keeps the `$ref`.
		    if (json.$ref) {
		        // a recursive key holds its own reference inside its definition, so a node already on the path is left alone rather than resolved again
		        if (visited.has(json))
		            return json;
		        visited.add(json);
		        const def = bySchema.get(json)?.def;
		        if (!def)
		            return json;
		        const inlined = stringifyKeyNames(bySchema, def, visited);
		        return inlined === def ? json : inlined;
		    }
		    for (const keyword of ["anyOf", "oneOf"]) {
		        const branches = json[keyword];
		        if (!Array.isArray(branches))
		            continue;
		        const mapped = branches.map((branch) => stringifyKeyNames(bySchema, branch, visited));
		        // rebuilding regardless would detach a key that had nothing to re-express, dropping its `$ref` and leaking the internal `id`
		        if (mapped.some((branch, i) => branch !== branches[i]))
		            json = { ...json, [keyword]: mapped };
		    }
		    // a member that already admits a string leaves the key unconstrained, so the node's own type re-expresses only when every member is numeric
		    const types = Array.isArray(json.type) ? json.type : [json.type];
		    const numericType = !types.includes("string") && types.some((t) => t === "number" || t === "integer");
		    // a heterogeneous key carries no type at all, so its numeric members are caught here instead
		    const values = json.enum ?? (json.const !== undefined ? [json.const] : undefined);
		    if (!numericType && !values?.some((v) => typeof v === "number"))
		        return json;
		    const { minimum, maximum, exclusiveMinimum, exclusiveMaximum, multipleOf, format, id, ...rest } = json;
		    if (rest.enum)
		        rest.enum = rest.enum.map((v) => (typeof v === "number" ? String(v) : v));
		    else if (typeof rest.const === "number")
		        rest.const = String(rest.const);
		    // a heterogeneous key keeps its absent type: the stringified members already say what a key may be
		    if (!numericType)
		        return rest;
		    rest.type = "string";
		    if (!values)
		        rest.pattern = (types.includes("number") ? regexes.number : regexes.integer).source;
		    return rest;
		}
		/** Every record of one conversion, so the carriers are found in a single pass rather than once per record. */
		const pendingRecords = new WeakMap();
		function rewriteKeyNames(ctx) {
		    // an extracted key is resolved by the object `extractToDef` left in its place, so the map is built once rather than searched per reference. `_zod.toJSONSchema` can hand the same object to two schemas, so the first entry carrying a body wins, as a search would have found it.
		    const bySchema = new Map();
		    for (const entry of ctx.seen.values()) {
		        if (entry.def && !bySchema.has(entry.schema))
		            bySchema.set(entry.schema, entry);
		    }
		    const rewrites = new Map();
		    for (const record of pendingRecords.get(ctx) ?? []) {
		        const seen = ctx.seen.get(record);
		        const names = (seen?.def ?? seen?.schema)?.propertyNames;
		        if (!names || names === true || rewrites.has(names))
		            continue;
		        const rewritten = stringifyKeyNames(bySchema, names, new Set());
		        if (rewritten !== names)
		            rewrites.set(names, rewritten);
		    }
		    if (!rewrites.size)
		        return;
		    // the flatten has already copied each record's own properties onto every wrapper by reference, and an extracted body is another such copy, so every carrier holding a rewritten key is updated together
		    for (const entry of ctx.seen.values()) {
		        for (const carrier of [entry.schema, entry.def]) {
		            const rewritten = carrier && rewrites.get(carrier.propertyNames);
		            if (rewritten)
		                carrier.propertyNames = rewritten;
		        }
		    }
		}
		const recordProcessor = (schema, ctx, _json, params) => {
		    const json = _json;
		    const def = schema._zod.def;
		    json.type = "object";
		    // For looseRecord with regex patterns, use patternProperties. This correctly represents "only validate keys matching the pattern" semantics and composes well with allOf (intersections)
		    const keyType = def.keyType;
		    const patterns = aggregateChecks(keyType).patterns;
		    if (def.mode === "loose" && patterns && patterns.size > 0) {
		        // Use patternProperties for looseRecord with regex patterns
		        const valueSchema = (0, to_json_schema_js_1.processSchema)(def.valueType, ctx, {
		            ...params,
		            path: [...params.path, "patternProperties", "*"],
		        });
		        json.patternProperties = {};
		        for (const pattern of patterns) {
		            (0, util_js_1.assignProp)(json.patternProperties, exactPattern(pattern).source, valueSchema);
		        }
		    }
		    else {
		        // Default behavior: use propertyNames + additionalProperties
		        if (ctx.target === "draft-07" || ctx.target === "draft-2020-12") {
		            json.propertyNames = (0, to_json_schema_js_1.processSchema)(def.keyType, ctx, {
		                ...params,
		                path: [...params.path, "propertyNames"],
		            });
		            let pending = pendingRecords.get(ctx);
		            if (!pending) {
		                pending = [];
		                pendingRecords.set(ctx, pending);
		                ctx.deferred.push(() => rewriteKeyNames(ctx));
		            }
		            pending.push(schema);
		        }
		        json.additionalProperties = (0, to_json_schema_js_1.processSchema)(def.valueType, ctx, {
		            ...params,
		            path: [...params.path, "additionalProperties"],
		        });
		    }
		    // Add required for keys with discrete values (enum, literal, etc.)
		    const keyValues = keyType._zod.values;
		    // Every key shares one value schema, so an optional-in value makes the whole key set omittable on input. Output keeps them: the exhaustive branch assigns every key, even one whose value came back undefined.
		    const omittableOnInput = ctx.io === "input" && inputOptin(def.valueType) !== undefined;
		    if (keyValues && !def.partial && !omittableOnInput) {
		        const validKeyValues = [...keyValues].filter((v) => typeof v === "string" || typeof v === "number");
		        if (validKeyValues.length > 0) {
		            json.required = validKeyValues.map(String);
		        }
		    }
		};
		exports.recordProcessor = recordProcessor;
		const nullableProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    const inner = (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    if (ctx.target === "openapi-3.0") {
		        seen.ref = def.innerType;
		        json.nullable = true;
		    }
		    else {
		        json.anyOf = [inner, { type: "null" }];
		    }
		};
		exports.nullableProcessor = nullableProcessor;
		const nonoptionalProcessor = (schema, ctx, _json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		};
		exports.nonoptionalProcessor = nonoptionalProcessor;
		/** Round-trips a default value through JSON so the emitted schema is guaranteed to be valid JSON.
		 * A BigInt has no reliable encoding, so it goes through `unrepresentable` like any other
		 * unrepresentable value. Returns a sentinel when the caller must not write a default of its own. */
		const UNREPRESENTABLE_DEFAULT = Symbol();
		function serializeDefaultValue(value, schema, ctx, json, params) {
		    let unrepresentable = false;
		    const serialized = JSON.stringify(value, (_, val) => {
		        if (typeof val !== "bigint")
		            return val;
		        unrepresentable = true;
		        return null;
		    });
		    if (!unrepresentable)
		        return JSON.parse(serialized);
		    (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "BigInt defaults cannot be represented in JSON Schema");
		    return UNREPRESENTABLE_DEFAULT;
		}
		const defaultProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		    const value = serializeDefaultValue(def.defaultValue, schema, ctx, json, params);
		    if (value !== UNREPRESENTABLE_DEFAULT)
		        json.default = value;
		};
		exports.defaultProcessor = defaultProcessor;
		const prefaultProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		    if (ctx.io !== "input")
		        return;
		    const value = serializeDefaultValue(def.defaultValue, schema, ctx, json, params);
		    if (value !== UNREPRESENTABLE_DEFAULT)
		        json._prefault = value;
		};
		exports.prefaultProcessor = prefaultProcessor;
		const catchProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		    let catchValue;
		    try {
		        catchValue = def.catchValue(undefined);
		    }
		    catch {
		        (0, to_json_schema_js_1.handleUnrepresentable)(schema, ctx, json, params, "Dynamic catch values are not supported in JSON Schema");
		        return;
		    }
		    json.default = catchValue;
		};
		exports.catchProcessor = catchProcessor;
		const pipeProcessor = (schema, ctx, _json, params) => {
		    const def = schema._zod.def;
		    const inIsTransform = def.in._zod.traits.has("$ZodTransform");
		    const innerType = ctx.io === "input" ? (inIsTransform ? def.out : def.in) : def.out;
		    (0, to_json_schema_js_1.processSchema)(innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = innerType;
		};
		exports.pipeProcessor = pipeProcessor;
		const readonlyProcessor = (schema, ctx, json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		    json.readOnly = true;
		};
		exports.readonlyProcessor = readonlyProcessor;
		const promiseProcessor = (schema, ctx, _json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		};
		exports.promiseProcessor = promiseProcessor;
		const optionalProcessor = (schema, ctx, _json, params) => {
		    const def = schema._zod.def;
		    (0, to_json_schema_js_1.processSchema)(def.innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = def.innerType;
		};
		exports.optionalProcessor = optionalProcessor;
		const lazyProcessor = (schema, ctx, _json, params) => {
		    const innerType = schema._zod.innerType;
		    (0, to_json_schema_js_1.processSchema)(innerType, ctx, params);
		    const seen = ctx.seen.get(schema);
		    seen.ref = innerType;
		};
		exports.lazyProcessor = lazyProcessor;
		// ==================== ALL PROCESSORS ====================
		exports.allProcessors = {
		    string: exports.stringProcessor,
		    number: exports.numberProcessor,
		    boolean: exports.booleanProcessor,
		    bigint: exports.bigintProcessor,
		    symbol: exports.symbolProcessor,
		    null: exports.nullProcessor,
		    undefined: exports.undefinedProcessor,
		    void: exports.voidProcessor,
		    never: exports.neverProcessor,
		    any: exports.anyProcessor,
		    unknown: exports.unknownProcessor,
		    date: exports.dateProcessor,
		    enum: exports.enumProcessor,
		    literal: exports.literalProcessor,
		    nan: exports.nanProcessor,
		    template_literal: exports.templateLiteralProcessor,
		    file: exports.fileProcessor,
		    success: exports.successProcessor,
		    custom: exports.customProcessor,
		    function: exports.functionProcessor,
		    transform: exports.transformProcessor,
		    map: exports.mapProcessor,
		    set: exports.setProcessor,
		    array: exports.arrayProcessor,
		    object: exports.objectProcessor,
		    union: exports.unionProcessor,
		    intersection: exports.intersectionProcessor,
		    tuple: exports.tupleProcessor,
		    record: exports.recordProcessor,
		    nullable: exports.nullableProcessor,
		    nonoptional: exports.nonoptionalProcessor,
		    default: exports.defaultProcessor,
		    prefault: exports.prefaultProcessor,
		    catch: exports.catchProcessor,
		    pipe: exports.pipeProcessor,
		    readonly: exports.readonlyProcessor,
		    promise: exports.promiseProcessor,
		    optional: exports.optionalProcessor,
		    lazy: exports.lazyProcessor,
		};
		function toJSONSchema(input, params) {
		    if ("_idmap" in input) {
		        // Registry case
		        const registry = input;
		        const ctx = (0, to_json_schema_js_1.initializeContext)({ ...params, processors: exports.allProcessors });
		        const defs = {};
		        // First pass: process all schemas to build the seen map
		        for (const entry of registry._idmap.entries()) {
		            const [_, schema] = entry;
		            (0, to_json_schema_js_1.processSchema)(schema, ctx);
		        }
		        const schemas = {};
		        const external = {
		            registry,
		            uri: params?.uri,
		            defs,
		        };
		        // Update the context with external configuration
		        ctx.external = external;
		        // Second pass: emit each schema
		        for (const entry of registry._idmap.entries()) {
		            const [key, schema] = entry;
		            (0, to_json_schema_js_1.extractDefs)(ctx, schema);
		            (0, util_js_1.assignProp)(schemas, key, (0, to_json_schema_js_1.finalize)(ctx, schema));
		        }
		        if (Object.keys(defs).length > 0) {
		            const defsSegment = ctx.target === "draft-2020-12" ? "$defs" : "definitions";
		            schemas.__shared = {
		                [defsSegment]: defs,
		            };
		        }
		        return { schemas };
		    }
		    // Single schema case
		    const ctx = (0, to_json_schema_js_1.initializeContext)({ ...params, processors: exports.allProcessors });
		    (0, to_json_schema_js_1.processSchema)(input, ctx);
		    (0, to_json_schema_js_1.extractDefs)(ctx, input);
		    return (0, to_json_schema_js_1.finalize)(ctx, input);
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (jsonSchemaProcessors));
	return jsonSchemaProcessors;
}

var jsonSchemaGenerator = {};

var hasRequiredJsonSchemaGenerator;

function requireJsonSchemaGenerator () {
	if (hasRequiredJsonSchemaGenerator) return jsonSchemaGenerator;
	hasRequiredJsonSchemaGenerator = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.JSONSchemaGenerator = void 0;
		const json_schema_processors_js_1 = /*@__PURE__*/ requireJsonSchemaProcessors();
		const to_json_schema_js_1 = /*@__PURE__*/ requireToJsonSchema();
		/**
		 * Legacy class-based interface for JSON Schema generation.
		 * This class wraps the new functional implementation to provide backward compatibility.
		 *
		 * @deprecated Use the `toJSONSchema` function instead for new code.
		 *
		 * @example
		 * ```typescript
		 * // Legacy usage (still supported)
		 * const gen = new JSONSchemaGenerator({ target: "draft-07" });
		 * gen.process(schema);
		 * const result = gen.emit(schema);
		 *
		 * // Preferred modern usage
		 * const result = toJSONSchema(schema, { target: "draft-07" });
		 * ```
		 */
		class JSONSchemaGenerator {
		    /** @deprecated Access via ctx instead */
		    get metadataRegistry() {
		        return this.ctx.metadataRegistry;
		    }
		    /** @deprecated Access via ctx instead */
		    get target() {
		        return this.ctx.target;
		    }
		    // annotated so the .d.cts emits an indexed access rather than an inline `import()` of an ESM path
		    /** @deprecated Access via ctx instead */
		    get unrepresentable() {
		        return this.ctx.unrepresentable;
		    }
		    /** @deprecated Access via ctx instead */
		    get override() {
		        return this.ctx.override;
		    }
		    /** @deprecated Access via ctx instead */
		    get io() {
		        return this.ctx.io;
		    }
		    /** @deprecated Access via ctx instead */
		    get counter() {
		        return this.ctx.counter;
		    }
		    set counter(value) {
		        this.ctx.counter = value;
		    }
		    /** @deprecated Access via ctx instead */
		    get seen() {
		        return this.ctx.seen;
		    }
		    constructor(params) {
		        // Normalize target for internal context
		        let normalizedTarget = params?.target ?? "draft-2020-12";
		        if (normalizedTarget === "draft-4")
		            normalizedTarget = "draft-04";
		        if (normalizedTarget === "draft-7")
		            normalizedTarget = "draft-07";
		        this.ctx = (0, to_json_schema_js_1.initializeContext)({
		            processors: json_schema_processors_js_1.allProcessors,
		            target: normalizedTarget,
		            ...(params?.metadata && { metadata: params.metadata }),
		            ...(params?.unrepresentable && { unrepresentable: params.unrepresentable }),
		            ...(params?.override && { override: params.override }),
		            ...(params?.io && { io: params.io }),
		        });
		    }
		    /**
		     * Process a schema to prepare it for JSON Schema generation.
		     * This must be called before emit().
		     */
		    process(schema, _params = { path: [], schemaPath: [] }) {
		        return (0, to_json_schema_js_1.processSchema)(schema, this.ctx, _params);
		    }
		    /**
		     * Emit the final JSON Schema after processing.
		     * Must call process() first.
		     */
		    emit(schema, _params) {
		        // Apply emit params to the context
		        if (_params) {
		            if (_params.cycles)
		                this.ctx.cycles = _params.cycles;
		            if (_params.reused)
		                this.ctx.reused = _params.reused;
		            if (_params.external)
		                this.ctx.external = _params.external;
		        }
		        // extractDefs/finalize skip their whole-map passes when they have already run for this `external`, but they also branch on `cycles`, `reused`, `seen.count` and the metadata registry — any of which can change between emits, including with no params at all. Drop the guards so the passes re-run. The registry conversion calls extractDefs/finalize directly and never routes through here, so it keeps skipping them.
		        this.ctx.sharedDefsExtractedFor = undefined;
		        this.ctx.sharedEmitDoneFor = undefined;
		        (0, to_json_schema_js_1.extractDefs)(this.ctx, schema);
		        const result = (0, to_json_schema_js_1.finalize)(this.ctx, schema);
		        // Strip ~standard property to match old implementation's return type
		        const { "~standard": _, ...plainResult } = result;
		        return plainResult;
		    }
		}
		exports.JSONSchemaGenerator = JSONSchemaGenerator;

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (jsonSchemaGenerator));
	return jsonSchemaGenerator;
}

var jsonSchema = {};

var hasRequiredJsonSchema;

function requireJsonSchema () {
	if (hasRequiredJsonSchema) return jsonSchema;
	hasRequiredJsonSchema = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (jsonSchema));
	return jsonSchema;
}

var hasRequiredCore;

function requireCore () {
	if (hasRequiredCore) return core$1;
	hasRequiredCore = 1;
	(function (exports) {
		var __createBinding = (core$1 && core$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (core$1 && core$1.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __exportStar = (core$1 && core$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		var __importStar = (core$1 && core$1.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.JSONSchema = exports.JSONSchemaGenerator = exports.toJSONSchema = exports.locales = exports.regexes = exports.toZod = exports.util = void 0;
		__exportStar(/*@__PURE__*/ requireCore$1(), exports);
		__exportStar(/*@__PURE__*/ requireParse$4(), exports);
		__exportStar(/*@__PURE__*/ requireErrors$3(), exports);
		__exportStar(/*@__PURE__*/ requireSchemas$1(), exports);
		__exportStar(/*@__PURE__*/ requireMemoizer(), exports);
		__exportStar(/*@__PURE__*/ requireChecks$1(), exports);
		__exportStar(/*@__PURE__*/ requireVersions(), exports);
		exports.util = __importStar(/*@__PURE__*/ requireUtil$2());
		var util_js_1 = /*@__PURE__*/ requireUtil$2();
		Object.defineProperty(exports, "toZod", { enumerable: true, get: function () { return util_js_1.toZod; } });
		exports.regexes = __importStar(/*@__PURE__*/ requireRegexes());
		exports.locales = __importStar(/*@__PURE__*/ requireLocales());
		__exportStar(/*@__PURE__*/ requireRegistries(), exports);
		__exportStar(/*@__PURE__*/ requireDoc(), exports);
		__exportStar(/*@__PURE__*/ requireCompile$3(), exports);
		__exportStar(/*@__PURE__*/ requireApi(), exports);
		__exportStar(/*@__PURE__*/ requireToJsonSchema(), exports);
		var json_schema_processors_js_1 = /*@__PURE__*/ requireJsonSchemaProcessors();
		Object.defineProperty(exports, "toJSONSchema", { enumerable: true, get: function () { return json_schema_processors_js_1.toJSONSchema; } });
		var json_schema_generator_js_1 = /*@__PURE__*/ requireJsonSchemaGenerator();
		Object.defineProperty(exports, "JSONSchemaGenerator", { enumerable: true, get: function () { return json_schema_generator_js_1.JSONSchemaGenerator; } });
		exports.JSONSchema = __importStar(/*@__PURE__*/ requireJsonSchema());

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (core$1));
	return core$1;
}

var schemas = {};

var checks = {};

var hasRequiredChecks;

function requireChecks () {
	if (hasRequiredChecks) return checks;
	hasRequiredChecks = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.slugify = exports.toUpperCase = exports.toLowerCase = exports.trim = exports.normalize = exports.overwrite = exports.mime = exports.properties = exports.property = exports.endsWith = exports.startsWith = exports.includes = exports.uppercase = exports.lowercase = exports.regex = exports.length = exports.minLength = exports.maxLength = exports.size = exports.minSize = exports.maxSize = exports.multipleOf = exports.nonnegative = exports.nonpositive = exports.negative = exports.positive = exports.gte = exports.gt = exports.lte = exports.lt = void 0;
		var index_js_1 = /*@__PURE__*/ requireCore();
		Object.defineProperty(exports, "lt", { enumerable: true, get: function () { return index_js_1._lt; } });
		Object.defineProperty(exports, "lte", { enumerable: true, get: function () { return index_js_1._lte; } });
		Object.defineProperty(exports, "gt", { enumerable: true, get: function () { return index_js_1._gt; } });
		Object.defineProperty(exports, "gte", { enumerable: true, get: function () { return index_js_1._gte; } });
		Object.defineProperty(exports, "positive", { enumerable: true, get: function () { return index_js_1._positive; } });
		Object.defineProperty(exports, "negative", { enumerable: true, get: function () { return index_js_1._negative; } });
		Object.defineProperty(exports, "nonpositive", { enumerable: true, get: function () { return index_js_1._nonpositive; } });
		Object.defineProperty(exports, "nonnegative", { enumerable: true, get: function () { return index_js_1._nonnegative; } });
		Object.defineProperty(exports, "multipleOf", { enumerable: true, get: function () { return index_js_1._multipleOf; } });
		Object.defineProperty(exports, "maxSize", { enumerable: true, get: function () { return index_js_1._maxSize; } });
		Object.defineProperty(exports, "minSize", { enumerable: true, get: function () { return index_js_1._minSize; } });
		Object.defineProperty(exports, "size", { enumerable: true, get: function () { return index_js_1._size; } });
		Object.defineProperty(exports, "maxLength", { enumerable: true, get: function () { return index_js_1._maxLength; } });
		Object.defineProperty(exports, "minLength", { enumerable: true, get: function () { return index_js_1._minLength; } });
		Object.defineProperty(exports, "length", { enumerable: true, get: function () { return index_js_1._length; } });
		Object.defineProperty(exports, "regex", { enumerable: true, get: function () { return index_js_1._regex; } });
		Object.defineProperty(exports, "lowercase", { enumerable: true, get: function () { return index_js_1._lowercase; } });
		Object.defineProperty(exports, "uppercase", { enumerable: true, get: function () { return index_js_1._uppercase; } });
		Object.defineProperty(exports, "includes", { enumerable: true, get: function () { return index_js_1._includes; } });
		Object.defineProperty(exports, "startsWith", { enumerable: true, get: function () { return index_js_1._startsWith; } });
		Object.defineProperty(exports, "endsWith", { enumerable: true, get: function () { return index_js_1._endsWith; } });
		Object.defineProperty(exports, "property", { enumerable: true, get: function () { return index_js_1._property; } });
		Object.defineProperty(exports, "properties", { enumerable: true, get: function () { return index_js_1._properties; } });
		Object.defineProperty(exports, "mime", { enumerable: true, get: function () { return index_js_1._mime; } });
		Object.defineProperty(exports, "overwrite", { enumerable: true, get: function () { return index_js_1._overwrite; } });
		Object.defineProperty(exports, "normalize", { enumerable: true, get: function () { return index_js_1._normalize; } });
		Object.defineProperty(exports, "trim", { enumerable: true, get: function () { return index_js_1._trim; } });
		Object.defineProperty(exports, "toLowerCase", { enumerable: true, get: function () { return index_js_1._toLowerCase; } });
		Object.defineProperty(exports, "toUpperCase", { enumerable: true, get: function () { return index_js_1._toUpperCase; } });
		Object.defineProperty(exports, "slugify", { enumerable: true, get: function () { return index_js_1._slugify; } });

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (checks));
	return checks;
}

var parse$2 = {};

var errors$1 = {};

var hasRequiredErrors$2;

function requireErrors$2 () {
	if (hasRequiredErrors$2) return errors$1;
	hasRequiredErrors$2 = 1;
	(function (exports) {
		var __createBinding = (errors$1 && errors$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (errors$1 && errors$1.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (errors$1 && errors$1.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodRealError = exports.ZodError = void 0;
		const core = __importStar(/*@__PURE__*/ requireCore());
		const index_js_1 = /*@__PURE__*/ requireCore();
		const util = __importStar(/*@__PURE__*/ requireUtil$2());
		/* Prototypes that already carry the lazy helper methods. Seeded with the
		 * intrinsics so that `init` on a foreign object — it accepts any object —
		 * can never install an accessor onto a prototype we do not own. */
		const _installedErrorProtos = /* @__PURE__ */ new WeakSet([Object.prototype, Error.prototype]);
		/* Helper methods live as non-enumerable lazy getters on the shared
		 * prototype instead of own properties on every instance. On first
		 * access the getter allocates the per-instance closure and caches it
		 * as a non-enumerable own property, so detached usage still works and
		 * the allocation only happens for methods actually touched. */
		function _lazyMethod(proto, key, make) {
		    Object.defineProperty(proto, key, {
		        configurable: true,
		        enumerable: false,
		        get() {
		            const value = make(this);
		            Object.defineProperty(this, key, { value, configurable: true, writable: true });
		            return value;
		        },
		        set(value) {
		            Object.defineProperty(this, key, { value, configurable: true, writable: true });
		        },
		    });
		}
		const initializer = (inst, issues) => {
		    index_js_1.$ZodError.init(inst, issues);
		    inst.name = "ZodError";
		    const proto = Object.getPrototypeOf(inst);
		    if (_installedErrorProtos.has(proto))
		        return;
		    _installedErrorProtos.add(proto);
		    _lazyMethod(proto, "format", (self) => (mapper) => core.formatError(self, mapper));
		    _lazyMethod(proto, "flatten", (self) => (mapper) => core.flattenError(self, mapper));
		    _lazyMethod(proto, "addIssue", (self) => (issue) => {
		        self.issues.push(issue);
		        self.message = JSON.stringify(self.issues, util.jsonStringifyReplacer, 2);
		    });
		    _lazyMethod(proto, "addIssues", (self) => (issues) => {
		        self.issues.push(...issues);
		        self.message = JSON.stringify(self.issues, util.jsonStringifyReplacer, 2);
		    });
		    Object.defineProperty(proto, "isEmpty", {
		        configurable: true,
		        enumerable: false,
		        get() {
		            return this.issues.length === 0;
		        },
		    });
		};
		exports.ZodError = core.$constructor("ZodError", initializer);
		exports.ZodRealError = core.$constructor("ZodError", initializer, undefined, {
		    Parent: Error,
		});
		// /** @deprecated Use `z.core.$ZodErrorMapCtx` instead. */
		// export type ErrorMapCtx = core.$ZodErrorMapCtx;

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (errors$1));
	return errors$1;
}

var hasRequiredParse$3;

function requireParse$3 () {
	if (hasRequiredParse$3) return parse$2;
	hasRequiredParse$3 = 1;
	(function (exports) {
		var __createBinding = (parse$2 && parse$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (parse$2 && parse$2.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (parse$2 && parse$2.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.safeDecodeAsync = exports.safeEncodeAsync = exports.safeDecode = exports.safeEncode = exports.decodeAsync = exports.encodeAsync = exports.decode = exports.encode = exports.validateAsync = exports.validate = exports.safeParseAsync = exports.safeParse = exports.parseAsync = exports.parse = void 0;
		const core = __importStar(/*@__PURE__*/ requireCore());
		const errors_js_1 = /*@__PURE__*/ requireErrors$2();
		exports.parse = core._parse(errors_js_1.ZodRealError);
		exports.parseAsync = core._parseAsync(errors_js_1.ZodRealError);
		exports.safeParse = core._safeParse(errors_js_1.ZodRealError);
		exports.safeParseAsync = core._safeParseAsync(errors_js_1.ZodRealError);
		var index_js_1 = /*@__PURE__*/ requireCore();
		Object.defineProperty(exports, "validate", { enumerable: true, get: function () { return index_js_1.validate; } });
		Object.defineProperty(exports, "validateAsync", { enumerable: true, get: function () { return index_js_1.validateAsync; } });
		// Codec functions
		exports.encode = core._encode(errors_js_1.ZodRealError);
		exports.decode = core._decode(errors_js_1.ZodRealError);
		exports.encodeAsync = core._encodeAsync(errors_js_1.ZodRealError);
		exports.decodeAsync = core._decodeAsync(errors_js_1.ZodRealError);
		exports.safeEncode = core._safeEncode(errors_js_1.ZodRealError);
		exports.safeDecode = core._safeDecode(errors_js_1.ZodRealError);
		exports.safeEncodeAsync = core._safeEncodeAsync(errors_js_1.ZodRealError);
		exports.safeDecodeAsync = core._safeDecodeAsync(errors_js_1.ZodRealError);

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (parse$2));
	return parse$2;
}

var hasRequiredSchemas;

function requireSchemas () {
	if (hasRequiredSchemas) return schemas;
	hasRequiredSchemas = 1;
	(function (exports) {
		var __createBinding = (schemas && schemas.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (schemas && schemas.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (schemas && schemas.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		var __importDefault = (schemas && schemas.__importDefault) || function (mod) {
		    return (mod && mod.__esModule) ? mod : { "default": mod };
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodIntersection = exports.ZodDiscriminatedUnion = exports.ZodXor = exports.ZodUnion = exports.ZodObject = exports.ZodArray = exports.ZodDate = exports.ZodVoid = exports.ZodNever = exports.ZodUnknown = exports.ZodAny = exports.ZodNull = exports.ZodUndefined = exports.ZodSymbol = exports.ZodBigIntFormat = exports.ZodBigInt = exports.ZodBoolean = exports.ZodNumberFormat = exports.ZodNumber = exports.ZodCustomStringFormat = exports.ZodJWT = exports.ZodIBAN = exports.ZodCreditCard = exports.ZodE164 = exports.ZodBase64URL = exports.ZodBase64 = exports.ZodCIDRv6 = exports.ZodCIDRv4 = exports.ZodIPv6 = exports.ZodMAC = exports.ZodIPv4 = exports.ZodKSUID = exports.ZodXID = exports.ZodULID = exports.ZodCUID2 = exports.ZodCUID = exports.ZodNanoID = exports.ZodEmoji = exports.ZodURL = exports.ZodUUID = exports.ZodGUID = exports.ZodEmail = exports.ZodISODuration = exports.ZodISOTime = exports.ZodISODate = exports.ZodISODateTime = exports.ZodStringFormat = exports.ZodString = exports._ZodString = exports.ZodType = void 0;
		exports.stringbool = exports.ZodInstanceOf = exports.meta = exports.describe = exports.ZodCustom = exports.ZodFunction = exports.ZodPromise = exports.ZodLazy = exports.ZodTemplateLiteral = exports.ZodReadonly = exports.ZodPreprocess = exports.ZodCodec = exports.ZodPipe = exports.ZodNaN = exports.ZodCatch = exports.ZodSuccess = exports.ZodNonOptional = exports.ZodPrefault = exports.ZodDefault = exports.ZodNullable = exports.ZodExactOptional = exports.ZodOptional = exports.ZodTransform = exports.ZodFile = exports.ZodLiteral = exports.ZodEnum = exports.ZodSet = exports.ZodMap = exports.ZodRecord = exports.ZodTuple = void 0;
		exports.string = string;
		exports.email = email;
		exports.guid = guid;
		exports.uuid = uuid;
		exports.uuidv4 = uuidv4;
		exports.uuidv6 = uuidv6;
		exports.uuidv7 = uuidv7;
		exports.url = url;
		exports.httpUrl = httpUrl;
		exports.emoji = emoji;
		exports.nanoid = nanoid;
		exports.cuid = cuid;
		exports.cuid2 = cuid2;
		exports.ulid = ulid;
		exports.xid = xid;
		exports.ksuid = ksuid;
		exports.ipv4 = ipv4;
		exports.mac = mac;
		exports.ipv6 = ipv6;
		exports.cidrv4 = cidrv4;
		exports.cidrv6 = cidrv6;
		exports.base64 = base64;
		exports.base64url = base64url;
		exports.e164 = e164;
		exports.creditCard = creditCard;
		exports.iban = iban;
		exports.jwt = jwt;
		exports.stringFormat = stringFormat;
		exports.hostname = hostname;
		exports.hex = hex;
		exports.currencyCode = currencyCode;
		exports.hash = hash;
		exports.number = number;
		exports.int = int;
		exports.float32 = float32;
		exports.float64 = float64;
		exports.int32 = int32;
		exports.uint32 = uint32;
		exports.boolean = boolean;
		exports.bigint = bigint;
		exports.int64 = int64;
		exports.uint64 = uint64;
		exports.symbol = symbol;
		exports.undefined = _undefined;
		exports.null = _null;
		exports.any = any;
		exports.unknown = unknown;
		exports.never = never;
		exports.void = _void;
		exports.date = date;
		exports.array = array;
		exports.keyof = keyof;
		exports.object = object;
		exports.strictObject = strictObject;
		exports.looseObject = looseObject;
		exports.union = union;
		exports.xor = xor;
		exports.discriminatedUnion = discriminatedUnion;
		exports.intersection = intersection;
		exports.tuple = tuple;
		exports.record = record;
		exports.partialRecord = partialRecord;
		exports.looseRecord = looseRecord;
		exports.map = map;
		exports.set = set;
		exports.enum = _enum;
		exports.nativeEnum = nativeEnum;
		exports.literal = literal;
		exports.file = file;
		exports.transform = transform;
		exports.optional = optional;
		exports.exactOptional = exactOptional;
		exports.nullable = nullable;
		exports.nullish = nullish;
		exports._default = _default;
		exports.prefault = prefault;
		exports.nonoptional = nonoptional;
		exports.success = success;
		exports.catch = _catch;
		exports.nan = nan;
		exports.pipe = pipe;
		exports.codec = codec;
		exports.invertCodec = invertCodec;
		exports.readonly = readonly;
		exports.templateLiteral = templateLiteral;
		exports.lazy = lazy;
		exports.promise = promise;
		exports._function = _function;
		exports.function = _function;
		exports._function = _function;
		exports.function = _function;
		exports.check = check;
		exports.custom = custom;
		exports.refine = refine;
		exports.superRefine = superRefine;
		exports.instanceof = _instanceof;
		exports.json = json;
		exports.preprocess = preprocess;
		const core = __importStar(/*@__PURE__*/ requireCore());
		const index_js_1 = /*@__PURE__*/ requireCore();
		const processors = __importStar(/*@__PURE__*/ requireJsonSchemaProcessors());
		const regexes = __importStar(/*@__PURE__*/ requireRegexes());
		const to_json_schema_js_1 = /*@__PURE__*/ requireToJsonSchema();
		const en_js_1 = __importDefault(/*@__PURE__*/ requireEn());
		const checks = __importStar(/*@__PURE__*/ requireChecks());
		const parse = __importStar(/*@__PURE__*/ requireParse$3());
		// Register English as the default locale on first ZodType construction. Hooked into the `ZodType` `$constructor` (rather than a top-level `config(en())` in `external.ts`) so bundlers honoring `sideEffects: false` can't tree-shake it out — see #5953, #5725. An explicit `z.config(z.locales.xx())` call wins regardless of order, since this only sets the default when none is present.
		function _ensureDefaultLocale() {
		    if (!core.globalConfig.localeError)
		        core.config((0, en_js_1.default)());
		}
		// the default memoizer is read by the core container init, which runs before `ZodType.init`, so each container calls this first
		function _ensureDefaultMemoizer() {
		    if (!core.globalConfig.memoizer)
		        core.config({ memoizer: core.memoizer() });
		}
		exports.ZodType = core.$constructor("ZodType", (inst, def) => {
		    _ensureDefaultLocale();
		    core.$ZodType.init(inst, def);
		    inst.def = def;
		    inst.type = def.type;
		    return inst;
		}, {
		    check(...chks) {
		        const def = this.def;
		        return this.clone(index_js_1.util.mergeDefs(def, {
		            checks: [
		                ...(def.checks ?? []),
		                ...chks.map((ch) => typeof ch === "function" ? { _zod: { check: ch, def: { check: "custom" }, onattach: [] } } : ch),
		            ],
		        }), { parent: true });
		    },
		    with(...chks) {
		        return this.check(...chks);
		    },
		    clone(def, params) {
		        return core.clone(this, def, params);
		    },
		    brand() {
		        return this;
		    },
		    register(reg, meta) {
		        reg.add(this, meta);
		        return this;
		    },
		    refine(check, params) {
		        return this.check(refine(check, params));
		    },
		    superRefine(refinement, params) {
		        return this.check(superRefine(refinement, params));
		    },
		    overwrite(fn) {
		        return this.check(checks.overwrite(fn));
		    },
		    optional() {
		        return optional(this);
		    },
		    exactOptional() {
		        return exactOptional(this);
		    },
		    nullable() {
		        return nullable(this);
		    },
		    nullish() {
		        return optional(nullable(this));
		    },
		    nonoptional(params) {
		        return nonoptional(this, params);
		    },
		    array() {
		        return array(this);
		    },
		    or(arg) {
		        return union([this, arg]);
		    },
		    and(arg) {
		        return intersection(this, arg);
		    },
		    transform(tx) {
		        return pipe(this, transform(tx));
		    },
		    default(d) {
		        return _default(this, d);
		    },
		    prefault(d) {
		        return prefault(this, d);
		    },
		    catch(params) {
		        return _catch(this, params);
		    },
		    pipe(target) {
		        return pipe(this, target);
		    },
		    readonly() {
		        return readonly(this);
		    },
		    describe(description) {
		        const cl = this.clone();
		        core.globalRegistry.add(cl, { description });
		        return cl;
		    },
		    meta(...args) {
		        // overloaded: meta() returns the registered metadata, meta(data) returns a clone with `data` registered. The mapped type picks up the second overload, so we accept variadic any-args and return `any` to satisfy both at runtime.
		        if (args.length === 0)
		            return core.globalRegistry.get(this);
		        const cl = this.clone();
		        core.globalRegistry.add(cl, args[0]);
		        return cl;
		    },
		    isOptional() {
		        return this.safeParse(undefined).success;
		    },
		    isNullable() {
		        return this.safeParse(null).success;
		    },
		    apply(fn, ...args) {
		        return args.length === 0 ? fn(this) : fn(this, ...args);
		    },
		    // Overrides core's `~standard` to add `jsonSchema`. Must stay a prototype entry: redefining it per instance demotes instances to dictionary mode.
		    get "~standard"() {
		        return index_js_1.util.hide(this, "~standard", {
		            ...core.standardProps(this),
		            jsonSchema: {
		                input: (0, to_json_schema_js_1.createStandardJSONSchemaMethod)(this, "input"),
		                output: (0, to_json_schema_js_1.createStandardJSONSchemaMethod)(this, "output"),
		            },
		        });
		    },
		    set "~standard"(value) {
		        index_js_1.util.own(this, "~standard", value);
		    },
		    parse: function _parse(data, params) {
		        return parse.parse(this, data, params, { callee: _parse });
		    },
		    parseAsync: async function _parseAsync(data, params) {
		        return await parse.parseAsync(this, data, params, { callee: _parseAsync });
		    },
		    safeParse(data, params) {
		        return parse.safeParse(this, data, params);
		    },
		    async safeParseAsync(data, params) {
		        return parse.safeParseAsync(this, data, params);
		    },
		    // `spa` is an alias: same function object as `safeParseAsync`, as before.
		    get spa() {
		        return this?.safeParseAsync;
		    },
		    set spa(value) {
		        index_js_1.util.own(this, "spa", value);
		    },
		    validate(data, params) {
		        return parse.validate(this, data, params);
		    },
		    validateAsync(data, params) {
		        return parse.validateAsync(this, data, params);
		    },
		    encode: function _encode(data, params) {
		        return parse.encode(this, data, params, { callee: _encode });
		    },
		    decode: function _decode(data, params) {
		        return parse.decode(this, data, params, { callee: _decode });
		    },
		    encodeAsync: async function _encodeAsync(data, params) {
		        return await parse.encodeAsync(this, data, params, { callee: _encodeAsync });
		    },
		    decodeAsync: async function _decodeAsync(data, params) {
		        return await parse.decodeAsync(this, data, params, { callee: _decodeAsync });
		    },
		    safeEncode(data, params) {
		        return parse.safeEncode(this, data, params);
		    },
		    safeDecode(data, params) {
		        return parse.safeDecode(this, data, params);
		    },
		    async safeEncodeAsync(data, params) {
		        return parse.safeEncodeAsync(this, data, params);
		    },
		    async safeDecodeAsync(data, params) {
		        return parse.safeDecodeAsync(this, data, params);
		    },
		    toJSONSchema(params) {
		        return (0, to_json_schema_js_1.createToJSONSchemaMethod)(this, {})(params);
		    },
		    // Reads through to the registry on every access, so it must not cache.
		    get description() {
		        return core.globalRegistry.get(this)?.description;
		    },
		    // No setter: `schema._def = x` throws, as it did when `_def` was a non-writable own property.
		    get _def() {
		        return this._zod.def;
		    },
		});
		/** @internal */
		exports._ZodString = core.$constructor("_ZodString", (inst, def) => {
		    core.$ZodString.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.stringProcessor(inst, ctx, json, params);
		}, 
		/*@__PURE__*/ index_js_1.util.derived({
		    format: (inst) => processors.aggregateChecks(inst).format ?? null,
		    minLength: (inst) => processors.aggregateChecks(inst).minimum ?? null,
		    maxLength: (inst) => processors.aggregateChecks(inst).maximum ?? null,
		}, {
		    regex(...args) {
		        return this.check(checks.regex(...args));
		    },
		    includes(...args) {
		        return this.check(checks.includes(...args));
		    },
		    startsWith(...args) {
		        return this.check(checks.startsWith(...args));
		    },
		    endsWith(...args) {
		        return this.check(checks.endsWith(...args));
		    },
		    min(...args) {
		        return this.check(checks.minLength(...args));
		    },
		    max(...args) {
		        return this.check(checks.maxLength(...args));
		    },
		    length(...args) {
		        return this.check(checks.length(...args));
		    },
		    nonempty(...args) {
		        return this.check(checks.minLength(1, ...args));
		    },
		    lowercase(params) {
		        return this.check(checks.lowercase(params));
		    },
		    uppercase(params) {
		        return this.check(checks.uppercase(params));
		    },
		    trim() {
		        return this.check(checks.trim());
		    },
		    normalize(...args) {
		        return this.check(checks.normalize(...args));
		    },
		    toLowerCase() {
		        return this.check(checks.toLowerCase());
		    },
		    toUpperCase() {
		        return this.check(checks.toUpperCase());
		    },
		    slugify() {
		        return this.check(checks.slugify());
		    },
		}));
		exports.ZodString = core.$constructor("ZodString", (inst, def) => {
		    core.$ZodString.init(inst, def);
		    exports._ZodString.init(inst, def);
		}, {
		    email(params) {
		        return this.check(core._email(exports.ZodEmail, params));
		    },
		    url(params) {
		        return this.check(core._url(exports.ZodURL, params));
		    },
		    jwt(params) {
		        return this.check(core._jwt(exports.ZodJWT, params));
		    },
		    emoji(params) {
		        return this.check(core._emoji(exports.ZodEmoji, params));
		    },
		    guid(params) {
		        return this.check(core._guid(exports.ZodGUID, params));
		    },
		    uuid(params) {
		        return this.check(core._uuid(exports.ZodUUID, params));
		    },
		    uuidv4(params) {
		        return this.check(core._uuidv4(exports.ZodUUID, params));
		    },
		    uuidv6(params) {
		        return this.check(core._uuidv6(exports.ZodUUID, params));
		    },
		    uuidv7(params) {
		        return this.check(core._uuidv7(exports.ZodUUID, params));
		    },
		    nanoid(params) {
		        return this.check(core._nanoid(exports.ZodNanoID, params));
		    },
		    cuid(params) {
		        return this.check(core._cuid(exports.ZodCUID, params));
		    },
		    cuid2(params) {
		        return this.check(core._cuid2(exports.ZodCUID2, params));
		    },
		    ulid(params) {
		        return this.check(core._ulid(exports.ZodULID, params));
		    },
		    base64(params) {
		        return this.check(core._base64(exports.ZodBase64, params));
		    },
		    base64url(params) {
		        return this.check(core._base64url(exports.ZodBase64URL, params));
		    },
		    xid(params) {
		        return this.check(core._xid(exports.ZodXID, params));
		    },
		    ksuid(params) {
		        return this.check(core._ksuid(exports.ZodKSUID, params));
		    },
		    ipv4(params) {
		        return this.check(core._ipv4(exports.ZodIPv4, params));
		    },
		    ipv6(params) {
		        return this.check(core._ipv6(exports.ZodIPv6, params));
		    },
		    cidrv4(params) {
		        return this.check(core._cidrv4(exports.ZodCIDRv4, params));
		    },
		    cidrv6(params) {
		        return this.check(core._cidrv6(exports.ZodCIDRv6, params));
		    },
		    e164(params) {
		        return this.check(core._e164(exports.ZodE164, params));
		    },
		    datetime(params) {
		        return this.check(core._isoDateTime(exports.ZodISODateTime, params));
		    },
		    date(params) {
		        return this.check(core._isoDate(exports.ZodISODate, params));
		    },
		    time(params) {
		        return this.check(core._isoTime(exports.ZodISOTime, params));
		    },
		    duration(params) {
		        return this.check(core._isoDuration(exports.ZodISODuration, params));
		    },
		});
		function string(params) {
		    return core._string(exports.ZodString, params);
		}
		exports.ZodStringFormat = core.$constructor("ZodStringFormat", (inst, def) => {
		    core.$ZodStringFormat.init(inst, def);
		    exports._ZodString.init(inst, def);
		});
		exports.ZodISODateTime = core.$constructor("ZodISODateTime", (inst, def) => {
		    core.$ZodISODateTime.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		exports.ZodISODate = core.$constructor("ZodISODate", (inst, def) => {
		    core.$ZodISODate.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		exports.ZodISOTime = core.$constructor("ZodISOTime", (inst, def) => {
		    core.$ZodISOTime.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		exports.ZodISODuration = core.$constructor("ZodISODuration", (inst, def) => {
		    core.$ZodISODuration.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		exports.ZodEmail = core.$constructor("ZodEmail", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodEmail.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function email(params) {
		    return core._email(exports.ZodEmail, params);
		}
		exports.ZodGUID = core.$constructor("ZodGUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodGUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function guid(params) {
		    return core._guid(exports.ZodGUID, params);
		}
		exports.ZodUUID = core.$constructor("ZodUUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodUUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function uuid(params) {
		    return core._uuid(exports.ZodUUID, params);
		}
		function uuidv4(params) {
		    return core._uuidv4(exports.ZodUUID, params);
		}
		// ZodUUIDv6
		function uuidv6(params) {
		    return core._uuidv6(exports.ZodUUID, params);
		}
		// ZodUUIDv7
		function uuidv7(params) {
		    return core._uuidv7(exports.ZodUUID, params);
		}
		exports.ZodURL = core.$constructor("ZodURL", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodURL.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function url(params) {
		    return core._url(exports.ZodURL, params);
		}
		function httpUrl(params) {
		    return core._url(exports.ZodURL, {
		        protocol: regexes.httpProtocol,
		        hostname: regexes.domain,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodEmoji = core.$constructor("ZodEmoji", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodEmoji.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function emoji(params) {
		    return core._emoji(exports.ZodEmoji, params);
		}
		exports.ZodNanoID = core.$constructor("ZodNanoID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodNanoID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function nanoid(params) {
		    return core._nanoid(exports.ZodNanoID, params);
		}
		/**
		 * @deprecated CUID v1 is deprecated by its authors due to information leakage
		 * (timestamps embedded in the id). Use {@link ZodCUID2} instead.
		 * See https://github.com/paralleldrive/cuid.
		 */
		exports.ZodCUID = core.$constructor("ZodCUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodCUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		/**
		 * Validates a CUID v1 string.
		 *
		 * @deprecated CUID v1 is deprecated by its authors due to information leakage
		 * (timestamps embedded in the id). Use {@link cuid2 | `z.cuid2()`} instead.
		 * See https://github.com/paralleldrive/cuid.
		 */
		function cuid(params) {
		    return core._cuid(exports.ZodCUID, params);
		}
		exports.ZodCUID2 = core.$constructor("ZodCUID2", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodCUID2.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cuid2(params) {
		    return core._cuid2(exports.ZodCUID2, params);
		}
		exports.ZodULID = core.$constructor("ZodULID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodULID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ulid(params) {
		    return core._ulid(exports.ZodULID, params);
		}
		exports.ZodXID = core.$constructor("ZodXID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodXID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function xid(params) {
		    return core._xid(exports.ZodXID, params);
		}
		exports.ZodKSUID = core.$constructor("ZodKSUID", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodKSUID.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ksuid(params) {
		    return core._ksuid(exports.ZodKSUID, params);
		}
		exports.ZodIPv4 = core.$constructor("ZodIPv4", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodIPv4.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ipv4(params) {
		    return core._ipv4(exports.ZodIPv4, params);
		}
		exports.ZodMAC = core.$constructor("ZodMAC", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodMAC.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function mac(params) {
		    return core._mac(exports.ZodMAC, params);
		}
		exports.ZodIPv6 = core.$constructor("ZodIPv6", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodIPv6.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function ipv6(params) {
		    return core._ipv6(exports.ZodIPv6, params);
		}
		exports.ZodCIDRv4 = core.$constructor("ZodCIDRv4", (inst, def) => {
		    core.$ZodCIDRv4.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cidrv4(params) {
		    return core._cidrv4(exports.ZodCIDRv4, params);
		}
		exports.ZodCIDRv6 = core.$constructor("ZodCIDRv6", (inst, def) => {
		    core.$ZodCIDRv6.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function cidrv6(params) {
		    return core._cidrv6(exports.ZodCIDRv6, params);
		}
		exports.ZodBase64 = core.$constructor("ZodBase64", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodBase64.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function base64(params) {
		    return core._base64(exports.ZodBase64, params);
		}
		exports.ZodBase64URL = core.$constructor("ZodBase64URL", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodBase64URL.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function base64url(params) {
		    return core._base64url(exports.ZodBase64URL, params);
		}
		exports.ZodE164 = core.$constructor("ZodE164", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodE164.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function e164(params) {
		    return core._e164(exports.ZodE164, params);
		}
		exports.ZodCreditCard = core.$constructor("ZodCreditCard", (inst, def) => {
		    core.$ZodCreditCard.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function creditCard(params) {
		    return core._creditCard(exports.ZodCreditCard, params);
		}
		exports.ZodIBAN = core.$constructor("ZodIBAN", (inst, def) => {
		    core.$ZodIBAN.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function iban(params) {
		    return core._iban(exports.ZodIBAN, params);
		}
		exports.ZodJWT = core.$constructor("ZodJWT", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodJWT.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function jwt(params) {
		    return core._jwt(exports.ZodJWT, params);
		}
		exports.ZodCustomStringFormat = core.$constructor("ZodCustomStringFormat", (inst, def) => {
		    // ZodStringFormat.init(inst, def);
		    core.$ZodCustomStringFormat.init(inst, def);
		    exports.ZodStringFormat.init(inst, def);
		});
		function stringFormat(format, fnOrRegex, _params = {}) {
		    return core._stringFormat(exports.ZodCustomStringFormat, format, fnOrRegex, _params);
		}
		function hostname(_params) {
		    return core._stringFormat(exports.ZodCustomStringFormat, "hostname", regexes.hostname, _params);
		}
		function hex(_params) {
		    return core._stringFormat(exports.ZodCustomStringFormat, "hex", regexes.hex, _params);
		}
		function currencyCode(_params) {
		    return core._stringFormat(exports.ZodCustomStringFormat, "currency_code", regexes.currencyCode, _params);
		}
		function hash(alg, params) {
		    const enc = params?.enc ?? "hex";
		    const format = `${alg}_${enc}`;
		    const regex = core.regexes[format];
		    if (!regex)
		        throw new Error(`Unrecognized hash format: ${format}`);
		    return core._stringFormat(exports.ZodCustomStringFormat, format, regex, params);
		}
		exports.ZodNumber = core.$constructor("ZodNumber", (inst, def) => {
		    core.$ZodNumber.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.numberProcessor(inst, ctx, json, params);
		    inst.isFinite = true;
		}, 
		/*@__PURE__*/ index_js_1.util.derived({
		    minValue: (inst) => {
		        const { minimum, exclusiveMinimum } = processors.aggregateChecks(inst);
		        return Math.max(minimum ?? Number.NEGATIVE_INFINITY, exclusiveMinimum ?? Number.NEGATIVE_INFINITY);
		    },
		    maxValue: (inst) => {
		        const { maximum, exclusiveMaximum } = processors.aggregateChecks(inst);
		        return Math.min(maximum ?? Number.POSITIVE_INFINITY, exclusiveMaximum ?? Number.POSITIVE_INFINITY);
		    },
		    isInt: (inst) => {
		        const { isInt, multipleOf } = processors.aggregateChecks(inst);
		        return !!isInt || !!multipleOf?.some(Number.isSafeInteger);
		    },
		    format: (inst) => processors.aggregateChecks(inst).format ?? null,
		}, {
		    gt(value, params) {
		        return this.check(checks.gt(value, params));
		    },
		    gte(value, params) {
		        return this.check(checks.gte(value, params));
		    },
		    min(value, params) {
		        return this.check(checks.gte(value, params));
		    },
		    lt(value, params) {
		        return this.check(checks.lt(value, params));
		    },
		    lte(value, params) {
		        return this.check(checks.lte(value, params));
		    },
		    max(value, params) {
		        return this.check(checks.lte(value, params));
		    },
		    int(params) {
		        return this.check(int(params));
		    },
		    safe(params) {
		        return this.check(int(params));
		    },
		    positive(params) {
		        return this.check(checks.gt(0, params));
		    },
		    nonnegative(params) {
		        return this.check(checks.gte(0, params));
		    },
		    negative(params) {
		        return this.check(checks.lt(0, params));
		    },
		    nonpositive(params) {
		        return this.check(checks.lte(0, params));
		    },
		    multipleOf(value, params) {
		        return this.check(checks.multipleOf(value, params));
		    },
		    step(value, params) {
		        return this.check(checks.multipleOf(value, params));
		    },
		    finite() {
		        return this;
		    },
		}));
		function number(params) {
		    return core._number(exports.ZodNumber, params);
		}
		exports.ZodNumberFormat = core.$constructor("ZodNumberFormat", (inst, def) => {
		    core.$ZodNumberFormat.init(inst, def);
		    exports.ZodNumber.init(inst, def);
		});
		function int(params) {
		    return core._int(exports.ZodNumberFormat, params);
		}
		function float32(params) {
		    return core._float32(exports.ZodNumberFormat, params);
		}
		function float64(params) {
		    return core._float64(exports.ZodNumberFormat, params);
		}
		function int32(params) {
		    return core._int32(exports.ZodNumberFormat, params);
		}
		function uint32(params) {
		    return core._uint32(exports.ZodNumberFormat, params);
		}
		exports.ZodBoolean = core.$constructor("ZodBoolean", (inst, def) => {
		    core.$ZodBoolean.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.booleanProcessor(inst, ctx, json, params);
		});
		function boolean(params) {
		    return core._boolean(exports.ZodBoolean, params);
		}
		exports.ZodBigInt = core.$constructor("ZodBigInt", (inst, def) => {
		    core.$ZodBigInt.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.bigintProcessor(inst, ctx, json, params);
		}, 
		/*@__PURE__*/ index_js_1.util.derived({
		    minValue: (inst) => processors.aggregateChecks(inst).minimum ?? null,
		    maxValue: (inst) => processors.aggregateChecks(inst).maximum ?? null,
		    format: (inst) => processors.aggregateChecks(inst).format ?? null,
		}, {
		    gte(value, params) {
		        return this.check(checks.gte(value, params));
		    },
		    min(value, params) {
		        return this.check(checks.gte(value, params));
		    },
		    gt(value, params) {
		        return this.check(checks.gt(value, params));
		    },
		    lt(value, params) {
		        return this.check(checks.lt(value, params));
		    },
		    lte(value, params) {
		        return this.check(checks.lte(value, params));
		    },
		    max(value, params) {
		        return this.check(checks.lte(value, params));
		    },
		    positive(params) {
		        return this.check(checks.gt(BigInt(0), params));
		    },
		    negative(params) {
		        return this.check(checks.lt(BigInt(0), params));
		    },
		    nonpositive(params) {
		        return this.check(checks.lte(BigInt(0), params));
		    },
		    nonnegative(params) {
		        return this.check(checks.gte(BigInt(0), params));
		    },
		    multipleOf(value, params) {
		        return this.check(checks.multipleOf(value, params));
		    },
		}));
		function bigint(params) {
		    return core._bigint(exports.ZodBigInt, params);
		}
		exports.ZodBigIntFormat = core.$constructor("ZodBigIntFormat", (inst, def) => {
		    core.$ZodBigIntFormat.init(inst, def);
		    exports.ZodBigInt.init(inst, def);
		});
		function int64(params) {
		    return core._int64(exports.ZodBigIntFormat, params);
		}
		function uint64(params) {
		    return core._uint64(exports.ZodBigIntFormat, params);
		}
		exports.ZodSymbol = core.$constructor("ZodSymbol", (inst, def) => {
		    core.$ZodSymbol.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.symbolProcessor(inst, ctx, json, params);
		});
		function symbol(params) {
		    return core._symbol(exports.ZodSymbol, params);
		}
		exports.ZodUndefined = core.$constructor("ZodUndefined", (inst, def) => {
		    core.$ZodUndefined.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.undefinedProcessor(inst, ctx, json, params);
		});
		function _undefined(params) {
		    return core._undefined(exports.ZodUndefined, params);
		}
		exports.ZodNull = core.$constructor("ZodNull", (inst, def) => {
		    core.$ZodNull.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.nullProcessor(inst, ctx, json, params);
		});
		function _null(params) {
		    return core._null(exports.ZodNull, params);
		}
		exports.ZodAny = core.$constructor("ZodAny", (inst, def) => {
		    core.$ZodAny.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.anyProcessor(inst, ctx, json, params);
		});
		function any() {
		    return core._any(exports.ZodAny);
		}
		exports.ZodUnknown = core.$constructor("ZodUnknown", (inst, def) => {
		    core.$ZodUnknown.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.unknownProcessor(inst, ctx, json, params);
		});
		function unknown() {
		    return core._unknown(exports.ZodUnknown);
		}
		exports.ZodNever = core.$constructor("ZodNever", (inst, def) => {
		    core.$ZodNever.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.neverProcessor(inst, ctx, json, params);
		});
		function never(params) {
		    return core._never(exports.ZodNever, params);
		}
		exports.ZodVoid = core.$constructor("ZodVoid", (inst, def) => {
		    core.$ZodVoid.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.voidProcessor(inst, ctx, json, params);
		});
		function _void(params) {
		    return core._void(exports.ZodVoid, params);
		}
		exports.ZodDate = core.$constructor("ZodDate", (inst, def) => {
		    core.$ZodDate.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.dateProcessor(inst, ctx, json, params);
		    inst.min = (value, params) => inst.check(checks.gte(value, params));
		    inst.max = (value, params) => inst.check(checks.lte(value, params));
		}, 
		/*@__PURE__*/ index_js_1.util.derived({
		    minDate: (inst) => {
		        const { minimum } = processors.aggregateChecks(inst);
		        return minimum ? new Date(minimum) : null;
		    },
		    maxDate: (inst) => {
		        const { maximum } = processors.aggregateChecks(inst);
		        return maximum ? new Date(maximum) : null;
		    },
		}, {}));
		function date(params) {
		    return core._date(exports.ZodDate, params);
		}
		exports.ZodArray = core.$constructor("ZodArray", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodArray.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.arrayProcessor(inst, ctx, json, params);
		    inst.element = def.element;
		}, {
		    min(n, params) {
		        return this.check(checks.minLength(n, params));
		    },
		    nonempty(params) {
		        return this.check(checks.minLength(1, params));
		    },
		    max(n, params) {
		        return this.check(checks.maxLength(n, params));
		    },
		    length(n, params) {
		        return this.check(checks.length(n, params));
		    },
		    unwrap() {
		        return this.element;
		    },
		});
		function array(element, params) {
		    return core._array(exports.ZodArray, element, params);
		}
		// .keyof
		function keyof(schema) {
		    const shape = schema._zod.def.shape;
		    return _enum(Object.keys(shape));
		}
		exports.ZodObject = core.$constructor("ZodObject", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodObjectJIT.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.objectProcessor(inst, ctx, json, params);
		    index_js_1.util.installLazyProp(inst, "shape", (self) => self._zod.def.shape, false);
		}, {
		    keyof() {
		        return _enum(Object.keys(this._zod.def.shape));
		    },
		    catchall(catchall) {
		        // `mergeDefs` rather than a spread: spreading reads `shape`, and resolving it can mint a whole fresh subtree
		        return this.clone(index_js_1.util.mergeDefs(this._zod.def, { catchall: catchall }));
		    },
		    passthrough() {
		        return this.clone(index_js_1.util.mergeDefs(this._zod.def, { catchall: unknown() }));
		    },
		    loose() {
		        return this.clone(index_js_1.util.mergeDefs(this._zod.def, { catchall: unknown() }));
		    },
		    strict() {
		        return this.clone(index_js_1.util.mergeDefs(this._zod.def, { catchall: never() }));
		    },
		    strip() {
		        return this.clone(index_js_1.util.mergeDefs(this._zod.def, { catchall: undefined }));
		    },
		    extend(incoming) {
		        return index_js_1.util.extend(this, incoming);
		    },
		    safeExtend(incoming) {
		        return index_js_1.util.safeExtend(this, incoming);
		    },
		    merge(other) {
		        return index_js_1.util.merge(this, other);
		    },
		    pick(mask) {
		        return index_js_1.util.pick(this, mask);
		    },
		    omit(mask) {
		        return index_js_1.util.omit(this, mask);
		    },
		    partial(...args) {
		        return index_js_1.util.partial(exports.ZodOptional, this, args[0]);
		    },
		    exactPartial(...args) {
		        return index_js_1.util.partial(exports.ZodExactOptional, this, args[0], "exactPartial");
		    },
		    required(...args) {
		        return index_js_1.util.required(exports.ZodNonOptional, this, args[0]);
		    },
		});
		function object(shape, params) {
		    const def = {
		        type: "object",
		        shape: shape ?? {},
		        ...index_js_1.util.normalizeParams(params),
		    };
		    return new exports.ZodObject(def);
		}
		// strictObject
		function strictObject(shape, params) {
		    return new exports.ZodObject({
		        type: "object",
		        shape,
		        catchall: never(),
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		// looseObject
		function looseObject(shape, params) {
		    return new exports.ZodObject({
		        type: "object",
		        shape,
		        catchall: unknown(),
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodUnion = core.$constructor("ZodUnion", (inst, def) => {
		    core.$ZodUnion.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.unionProcessor(inst, ctx, json, params);
		    inst.options = def.options;
		});
		function union(options, params) {
		    return new exports.ZodUnion({
		        type: "union",
		        options: options,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodXor = core.$constructor("ZodXor", (inst, def) => {
		    exports.ZodUnion.init(inst, def);
		    core.$ZodXor.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.unionProcessor(inst, ctx, json, params);
		    inst.options = def.options;
		});
		/** Creates an exclusive union (XOR) where exactly one option must match.
		 * Unlike regular unions that succeed when any option matches, xor fails if
		 * zero or more than one option matches the input. */
		function xor(options, params) {
		    return new exports.ZodXor({
		        type: "union",
		        options: options,
		        inclusive: false,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodDiscriminatedUnion = core.$constructor("ZodDiscriminatedUnion", (inst, def) => {
		    exports.ZodUnion.init(inst, def);
		    core.$ZodDiscriminatedUnion.init(inst, def);
		});
		function discriminatedUnion(discriminator, options, params) {
		    // const [options, params] = args;
		    return new exports.ZodDiscriminatedUnion({
		        type: "union",
		        options: options,
		        discriminator,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodIntersection = core.$constructor("ZodIntersection", (inst, def) => {
		    core.$ZodIntersection.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.intersectionProcessor(inst, ctx, json, params);
		});
		function intersection(left, right) {
		    return new exports.ZodIntersection({
		        type: "intersection",
		        left: left,
		        right: right,
		    });
		}
		exports.ZodTuple = core.$constructor("ZodTuple", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodTuple.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.tupleProcessor(inst, ctx, json, params);
		}, {
		    rest(rest) {
		        return this.clone({
		            ...this._zod.def,
		            rest: rest,
		        });
		    },
		    partial() {
		        const def = this._zod.def;
		        // a refinement was authored against the full arity; partialing would run it on a shorter array
		        if (def.checks?.length)
		            throw new Error(".partial() cannot be used on tuple schemas containing refinements");
		        return this.clone({
		            ...def,
		            items: def.items.map((item) => new exports.ZodOptional({ type: "optional", innerType: item })),
		        });
		    },
		});
		function tuple(items, _paramsOrRest, _params) {
		    const hasRest = _paramsOrRest instanceof core.$ZodType;
		    const params = hasRest ? _params : _paramsOrRest;
		    const rest = hasRest ? _paramsOrRest : null;
		    return new exports.ZodTuple({
		        type: "tuple",
		        items: items,
		        rest,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodRecord = core.$constructor("ZodRecord", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodRecord.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.recordProcessor(inst, ctx, json, params);
		    inst.keyType = def.keyType;
		    inst.valueType = def.valueType;
		});
		function record(keyType, valueType, params) {
		    // v3-compat: z.record(valueType, params?) — defaults keyType to z.string()
		    if (!valueType || !valueType._zod) {
		        return new exports.ZodRecord({
		            type: "record",
		            keyType: string(),
		            valueType: keyType,
		            ...index_js_1.util.normalizeParams(valueType),
		        });
		    }
		    return new exports.ZodRecord({
		        type: "record",
		        keyType,
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		// type alksjf = core.output<core.$ZodRecordKey>;
		function partialRecord(keyType, valueType, params) {
		    return new exports.ZodRecord({
		        type: "record",
		        keyType,
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		        partial: true,
		    });
		}
		function looseRecord(keyType, valueType, params) {
		    return new exports.ZodRecord({
		        type: "record",
		        keyType,
		        valueType: valueType,
		        mode: "loose",
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodMap = core.$constructor("ZodMap", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodMap.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.mapProcessor(inst, ctx, json, params);
		    inst.keyType = def.keyType;
		    inst.valueType = def.valueType;
		    inst.min = (...args) => inst.check(core._minSize(...args));
		    inst.nonempty = (params) => inst.check(core._minSize(1, params));
		    inst.max = (...args) => inst.check(core._maxSize(...args));
		    inst.size = (...args) => inst.check(core._size(...args));
		});
		function map(keyType, valueType, params) {
		    return new exports.ZodMap({
		        type: "map",
		        keyType: keyType,
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodSet = core.$constructor("ZodSet", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodSet.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.setProcessor(inst, ctx, json, params);
		    inst.min = (...args) => inst.check(core._minSize(...args));
		    inst.nonempty = (params) => inst.check(core._minSize(1, params));
		    inst.max = (...args) => inst.check(core._maxSize(...args));
		    inst.size = (...args) => inst.check(core._size(...args));
		});
		function set(valueType, params) {
		    return new exports.ZodSet({
		        type: "set",
		        valueType: valueType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodEnum = core.$constructor("ZodEnum", (inst, def) => {
		    core.$ZodEnum.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.enumProcessor(inst, ctx, json, params);
		    inst.enum = def.entries;
		    // reuse the parsed value set so a numeric TS enum's reverse-mapping keys stay out
		    inst.options = [...inst._zod.values];
		    const keys = new Set(Object.keys(def.entries));
		    inst.extract = (values, params) => {
		        const newEntries = {};
		        for (const value of values) {
		            if (keys.has(value)) {
		                newEntries[value] = def.entries[value];
		            }
		            else
		                throw new Error(`Key ${value} not found in enum`);
		        }
		        return new exports.ZodEnum({
		            ...def,
		            checks: [],
		            ...index_js_1.util.normalizeParams(params),
		            entries: newEntries,
		        });
		    };
		    inst.exclude = (values, params) => {
		        const newEntries = { ...def.entries };
		        for (const value of values) {
		            if (keys.has(value)) {
		                delete newEntries[value];
		            }
		            else
		                throw new Error(`Key ${value} not found in enum`);
		        }
		        return new exports.ZodEnum({
		            ...def,
		            checks: [],
		            ...index_js_1.util.normalizeParams(params),
		            entries: newEntries,
		        });
		    };
		});
		function _enum(values, params) {
		    const entries = Array.isArray(values) ? Object.fromEntries(values.map((v) => [v, v])) : values;
		    return new exports.ZodEnum({
		        type: "enum",
		        entries,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		/** @deprecated This API has been merged into `z.enum()`. Use `z.enum()` instead.
		 *
		 * ```ts
		 * enum Colors { red, green, blue }
		 * z.enum(Colors);
		 * ```
		 */
		function nativeEnum(entries, params) {
		    return new exports.ZodEnum({
		        type: "enum",
		        entries,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodLiteral = core.$constructor("ZodLiteral", (inst, def) => {
		    core.$ZodLiteral.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.literalProcessor(inst, ctx, json, params);
		    inst.values = new Set(def.values);
		    Object.defineProperty(inst, "value", {
		        get() {
		            if (def.values.length > 1) {
		                throw new Error("This schema contains multiple valid literal values. Use `.values` instead.");
		            }
		            return def.values[0];
		        },
		    });
		});
		function literal(value, params) {
		    return new exports.ZodLiteral({
		        type: "literal",
		        values: Array.isArray(value) ? value : [value],
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodFile = core.$constructor("ZodFile", (inst, def) => {
		    core.$ZodFile.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.fileProcessor(inst, ctx, json, params);
		    inst.min = (size, params) => inst.check(core._minSize(size, params));
		    inst.max = (size, params) => inst.check(core._maxSize(size, params));
		    inst.mime = (types, params) => inst.check(core._mime(Array.isArray(types) ? types : [types], params));
		});
		function file(params) {
		    return core._file(exports.ZodFile, params);
		}
		exports.ZodTransform = core.$constructor("ZodTransform", (inst, def) => {
		    _ensureDefaultMemoizer();
		    core.$ZodTransform.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.transformProcessor(inst, ctx, json, params);
		    inst._zod.parse = (payload, _ctx) => {
		        if (_ctx.direction === "backward") {
		            throw new core.$ZodEncodeError(inst.constructor.name);
		        }
		        payload.addIssue = (issue) => {
		            if (typeof issue === "string") {
		                payload.issues.push(index_js_1.util.issue(issue, payload.value, def));
		            }
		            else {
		                // for Zod 3 backwards compatibility
		                const _issue = issue;
		                if (_issue.fatal)
		                    _issue.continue = false;
		                _issue.code ?? (_issue.code = "custom");
		                if (!("input" in _issue))
		                    _issue.input = payload.value;
		                _issue.inst ?? (_issue.inst = inst);
		                // _issue.continue ??= true;
		                payload.issues.push(index_js_1.util.issue(_issue));
		            }
		        };
		        const output = def.transform(payload.value, payload);
		        if (output instanceof Promise) {
		            return output.then((output) => {
		                payload.value = output;
		                return payload;
		            });
		        }
		        payload.value = output;
		        return payload;
		    };
		});
		function transform(fn) {
		    return new exports.ZodTransform({
		        type: "transform",
		        transform: fn,
		    });
		}
		exports.ZodOptional = core.$constructor("ZodOptional", (inst, def) => {
		    core.$ZodOptional.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.optionalProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function optional(innerType) {
		    return new exports.ZodOptional({
		        type: "optional",
		        innerType: innerType,
		    });
		}
		exports.ZodExactOptional = core.$constructor("ZodExactOptional", (inst, def) => {
		    core.$ZodExactOptional.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.optionalProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function exactOptional(innerType) {
		    return new exports.ZodExactOptional({
		        type: "optional",
		        innerType: innerType,
		    });
		}
		exports.ZodNullable = core.$constructor("ZodNullable", (inst, def) => {
		    core.$ZodNullable.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.nullableProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function nullable(innerType) {
		    return new exports.ZodNullable({
		        type: "nullable",
		        innerType: innerType,
		    });
		}
		// nullish
		function nullish(innerType) {
		    return optional(nullable(innerType));
		}
		exports.ZodDefault = core.$constructor("ZodDefault", (inst, def) => {
		    core.$ZodDefault.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.defaultProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		    inst.removeDefault = inst.unwrap;
		});
		function _default(innerType, defaultValue) {
		    return new exports.ZodDefault({
		        type: "default",
		        innerType: innerType,
		        get defaultValue() {
		            return typeof defaultValue === "function" ? defaultValue() : index_js_1.util.shallowClone(defaultValue);
		        },
		    });
		}
		exports.ZodPrefault = core.$constructor("ZodPrefault", (inst, def) => {
		    core.$ZodPrefault.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.prefaultProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function prefault(innerType, defaultValue) {
		    return new exports.ZodPrefault({
		        type: "prefault",
		        innerType: innerType,
		        get defaultValue() {
		            return typeof defaultValue === "function" ? defaultValue() : index_js_1.util.shallowClone(defaultValue);
		        },
		    });
		}
		exports.ZodNonOptional = core.$constructor("ZodNonOptional", (inst, def) => {
		    core.$ZodNonOptional.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.nonoptionalProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function nonoptional(innerType, params) {
		    return new exports.ZodNonOptional({
		        type: "nonoptional",
		        innerType: innerType,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodSuccess = core.$constructor("ZodSuccess", (inst, def) => {
		    core.$ZodSuccess.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.successProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function success(innerType) {
		    return new exports.ZodSuccess({
		        type: "success",
		        innerType: innerType,
		    });
		}
		exports.ZodCatch = core.$constructor("ZodCatch", (inst, def) => {
		    core.$ZodCatch.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.catchProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		    inst.removeCatch = inst.unwrap;
		});
		function _catch(innerType, catchValue) {
		    return new exports.ZodCatch({
		        type: "catch",
		        innerType: innerType,
		        catchValue: (typeof catchValue === "function" ? catchValue : core.util.constantCatch(catchValue)),
		    });
		}
		exports.ZodNaN = core.$constructor("ZodNaN", (inst, def) => {
		    core.$ZodNaN.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.nanProcessor(inst, ctx, json, params);
		});
		function nan(params) {
		    return core._nan(exports.ZodNaN, params);
		}
		exports.ZodPipe = core.$constructor("ZodPipe", (inst, def) => {
		    core.$ZodPipe.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.pipeProcessor(inst, ctx, json, params);
		    inst.in = def.in;
		    inst.out = def.out;
		});
		function pipe(in_, out) {
		    return new exports.ZodPipe({
		        type: "pipe",
		        in: in_,
		        out: out,
		        // ...util.normalizeParams(params),
		    });
		}
		exports.ZodCodec = core.$constructor("ZodCodec", (inst, def) => {
		    exports.ZodPipe.init(inst, def);
		    core.$ZodCodec.init(inst, def);
		});
		function codec(in_, out, params) {
		    return new exports.ZodCodec({
		        type: "pipe",
		        in: in_,
		        out: out,
		        transform: params.decode,
		        reverseTransform: params.encode,
		    });
		}
		function invertCodec(codec) {
		    const def = codec._zod.def;
		    return new exports.ZodCodec({
		        type: "pipe",
		        in: def.out,
		        out: def.in,
		        transform: def.reverseTransform,
		        reverseTransform: def.transform,
		    });
		}
		exports.ZodPreprocess = core.$constructor("ZodPreprocess", (inst, def) => {
		    exports.ZodPipe.init(inst, def);
		    core.$ZodPreprocess.init(inst, def);
		});
		exports.ZodReadonly = core.$constructor("ZodReadonly", (inst, def) => {
		    core.$ZodReadonly.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.readonlyProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function readonly(innerType) {
		    return new exports.ZodReadonly({
		        type: "readonly",
		        innerType: innerType,
		    });
		}
		exports.ZodTemplateLiteral = core.$constructor("ZodTemplateLiteral", (inst, def) => {
		    core.$ZodTemplateLiteral.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.templateLiteralProcessor(inst, ctx, json, params);
		});
		function templateLiteral(parts, params) {
		    return new exports.ZodTemplateLiteral({
		        type: "template_literal",
		        parts,
		        ...index_js_1.util.normalizeParams(params),
		    });
		}
		exports.ZodLazy = core.$constructor("ZodLazy", (inst, def) => {
		    core.$ZodLazy.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.lazyProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.getter();
		});
		function lazy(getter) {
		    return new exports.ZodLazy({
		        type: "lazy",
		        getter: getter,
		    });
		}
		exports.ZodPromise = core.$constructor("ZodPromise", (inst, def) => {
		    core.$ZodPromise.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.promiseProcessor(inst, ctx, json, params);
		    inst.unwrap = () => inst._zod.def.innerType;
		});
		function promise(innerType) {
		    return new exports.ZodPromise({
		        type: "promise",
		        innerType: innerType,
		    });
		}
		exports.ZodFunction = core.$constructor("ZodFunction", (inst, def) => {
		    core.$ZodFunction.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.functionProcessor(inst, ctx, json, params);
		});
		function _function(params) {
		    return new exports.ZodFunction({
		        type: "function",
		        input: Array.isArray(params?.input) ? tuple(params?.input) : (params?.input ?? array(unknown())),
		        output: params?.output ?? unknown(),
		    });
		}
		exports.ZodCustom = core.$constructor("ZodCustom", (inst, def) => {
		    core.$ZodCustom.init(inst, def);
		    exports.ZodType.init(inst, def);
		    inst._zod.processJSONSchema = (ctx, json, params) => processors.customProcessor(inst, ctx, json, params);
		});
		// custom checks
		function check(fn) {
		    const ch = new core.$ZodCheck({
		        check: "custom",
		        // ...util.normalizeParams(params),
		    });
		    ch._zod.check = fn;
		    return ch;
		}
		function custom(fn, _params) {
		    return core._custom(exports.ZodCustom, fn ?? (() => true), _params);
		}
		function refine(fn, _params = {}) {
		    return core._refine(exports.ZodCustom, fn, _params);
		}
		// superRefine
		function superRefine(fn, params) {
		    return core._superRefine(fn, params);
		}
		// Re-export describe and meta from core
		exports.describe = core.describe;
		exports.meta = core.meta;
		exports.ZodInstanceOf = core.$constructor("ZodInstanceOf", (inst, def) => {
		    exports.ZodCustom.init(inst, def);
		}, {
		    properties(shape, params) {
		        // asserts in place, so the narrowed output type is truthful without a wrapper
		        return this.check(core._properties(shape, params));
		    },
		});
		function _instanceof(cls, params = {}) {
		    const inst = new exports.ZodInstanceOf({
		        type: "custom",
		        check: "custom",
		        fn: (data) => data instanceof cls,
		        abort: true,
		        ...index_js_1.util.normalizeParams(params),
		    });
		    inst._zod.bag.Class = cls;
		    // Override check to emit invalid_type instead of custom
		    inst._zod.check = (payload) => {
		        if (!(payload.value instanceof cls)) {
		            payload.issues.push({
		                code: "invalid_type",
		                expected: cls.name,
		                input: payload.value,
		                inst,
		                path: [...(inst._zod.def.path ?? [])],
		            });
		        }
		    };
		    return inst;
		}
		// stringbool
		const stringbool = (...args) => core._stringbool({
		    Codec: exports.ZodCodec,
		    Boolean: exports.ZodBoolean,
		    String: exports.ZodString,
		}, ...args);
		exports.stringbool = stringbool;
		function json(params) {
		    const jsonSchema = lazy(() => {
		        return union([string(params), number(), boolean(), _null(), array(jsonSchema), record(string(), jsonSchema)]);
		    });
		    return jsonSchema;
		}
		// preprocess
		function preprocess(fn, schema) {
		    return new exports.ZodPreprocess({
		        type: "pipe",
		        in: transform(fn),
		        out: schema,
		    });
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (schemas));
	return schemas;
}

var compat = {};

var hasRequiredCompat;

function requireCompat () {
	if (hasRequiredCompat) return compat;
	hasRequiredCompat = 1;
	(function (exports) {
		// Zod 3 compat layer
		var __createBinding = (compat && compat.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (compat && compat.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (compat && compat.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodFirstPartyTypeKind = exports.config = exports.$brand = exports.ZodIssueCode = void 0;
		exports.setErrorMap = setErrorMap;
		exports.getErrorMap = getErrorMap;
		const core = __importStar(/*@__PURE__*/ requireCore());
		/** @deprecated Use the raw string literal codes instead, e.g. "invalid_type". */
		exports.ZodIssueCode = {
		    invalid_type: "invalid_type",
		    too_big: "too_big",
		    too_small: "too_small",
		    invalid_format: "invalid_format",
		    not_multiple_of: "not_multiple_of",
		    unrecognized_keys: "unrecognized_keys",
		    invalid_union: "invalid_union",
		    invalid_key: "invalid_key",
		    invalid_element: "invalid_element",
		    invalid_value: "invalid_value",
		    custom: "custom",
		};
		var index_js_1 = /*@__PURE__*/ requireCore();
		Object.defineProperty(exports, "$brand", { enumerable: true, get: function () { return index_js_1.$brand; } });
		Object.defineProperty(exports, "config", { enumerable: true, get: function () { return index_js_1.config; } });
		/** @deprecated Use `z.config(params)` instead. */
		function setErrorMap(map) {
		    core.config({
		        customError: map,
		    });
		}
		/** @deprecated Use `z.config()` instead. */
		function getErrorMap() {
		    return core.config().customError;
		}
		/** @deprecated Do not use. Stub definition, only included for zod-to-json-schema compatibility. */
		var ZodFirstPartyTypeKind;
		(function (ZodFirstPartyTypeKind) {
		})(ZodFirstPartyTypeKind || (exports.ZodFirstPartyTypeKind = ZodFirstPartyTypeKind = {}));

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (compat));
	return compat;
}

var fromJsonSchema = {};

var iso = {};

var hasRequiredIso;

function requireIso () {
	if (hasRequiredIso) return iso;
	hasRequiredIso = 1;
	(function (exports) {
		var __createBinding = (iso && iso.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (iso && iso.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (iso && iso.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ZodISOTime = exports.ZodISODuration = exports.ZodISODateTime = exports.ZodISODate = void 0;
		exports.datetime = datetime;
		exports.date = date;
		exports.time = time;
		exports.duration = duration;
		const core = __importStar(/*@__PURE__*/ requireCore());
		const schemas_js_1 = /*@__PURE__*/ requireSchemas();
		var schemas_js_2 = /*@__PURE__*/ requireSchemas();
		Object.defineProperty(exports, "ZodISODate", { enumerable: true, get: function () { return schemas_js_2.ZodISODate; } });
		Object.defineProperty(exports, "ZodISODateTime", { enumerable: true, get: function () { return schemas_js_2.ZodISODateTime; } });
		Object.defineProperty(exports, "ZodISODuration", { enumerable: true, get: function () { return schemas_js_2.ZodISODuration; } });
		Object.defineProperty(exports, "ZodISOTime", { enumerable: true, get: function () { return schemas_js_2.ZodISOTime; } });
		function datetime(params) {
		    return core._isoDateTime(schemas_js_1.ZodISODateTime, params);
		}
		function date(params) {
		    return core._isoDate(schemas_js_1.ZodISODate, params);
		}
		function time(params) {
		    return core._isoTime(schemas_js_1.ZodISOTime, params);
		}
		function duration(params) {
		    return core._isoDuration(schemas_js_1.ZodISODuration, params);
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (iso));
	return iso;
}

var hasRequiredFromJsonSchema;

function requireFromJsonSchema () {
	if (hasRequiredFromJsonSchema) return fromJsonSchema;
	hasRequiredFromJsonSchema = 1;
	(function (exports) {
		var __createBinding = (fromJsonSchema && fromJsonSchema.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (fromJsonSchema && fromJsonSchema.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (fromJsonSchema && fromJsonSchema.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.fromJSONSchema = fromJSONSchema;
		const registries_js_1 = /*@__PURE__*/ requireRegistries();
		const util_js_1 = /*@__PURE__*/ requireUtil$2();
		const _checks = __importStar(/*@__PURE__*/ requireChecks());
		const _iso = __importStar(/*@__PURE__*/ requireIso());
		const _schemas = __importStar(/*@__PURE__*/ requireSchemas());
		// Local z object to avoid circular dependency with ../index.js
		const z = {
		    ..._schemas,
		    ..._checks,
		    iso: _iso,
		};
		// Keys that are recognized and handled by the conversion logic
		const RECOGNIZED_KEYS = /*@__PURE__*/ new Set([
		    // Schema identification
		    "$schema",
		    "$ref",
		    "$defs",
		    "definitions",
		    // Core schema keywords
		    "$id",
		    "id",
		    "$comment",
		    "$anchor",
		    "$vocabulary",
		    "$dynamicRef",
		    "$dynamicAnchor",
		    // Type
		    "type",
		    "enum",
		    "const",
		    // Composition
		    "anyOf",
		    "oneOf",
		    "allOf",
		    "not",
		    // Object
		    "properties",
		    "required",
		    "additionalProperties",
		    "patternProperties",
		    "propertyNames",
		    "minProperties",
		    "maxProperties",
		    // Array
		    "items",
		    "prefixItems",
		    "additionalItems",
		    "minItems",
		    "maxItems",
		    "uniqueItems",
		    "contains",
		    "minContains",
		    "maxContains",
		    // String
		    "minLength",
		    "maxLength",
		    "pattern",
		    "format",
		    // Number
		    "minimum",
		    "maximum",
		    "exclusiveMinimum",
		    "exclusiveMaximum",
		    "multipleOf",
		    // Already handled metadata
		    "description",
		    "default",
		    // Content
		    "contentEncoding",
		    "contentMediaType",
		    "contentSchema",
		    // Unsupported (error-throwing)
		    "unevaluatedItems",
		    "unevaluatedProperties",
		    "if",
		    "then",
		    "else",
		    "dependentSchemas",
		    "dependentRequired",
		    // OpenAPI
		    "nullable",
		    "readOnly",
		]);
		function detectVersion(schema, defaultTarget) {
		    const $schema = schema.$schema;
		    if ($schema === "https://json-schema.org/draft/2020-12/schema") {
		        return "draft-2020-12";
		    }
		    if ($schema === "http://json-schema.org/draft-07/schema#") {
		        return "draft-7";
		    }
		    if ($schema === "http://json-schema.org/draft-04/schema#") {
		        return "draft-4";
		    }
		    // Use defaultTarget if provided, otherwise default to draft-2020-12
		    return defaultTarget ?? "draft-2020-12";
		}
		// Positional schemas constrain the elements that are present; only minItems makes them required.
		function applyMinItems(items, minItems) {
		    return items.map((item, index) => (index < minItems ? item : item.optional()));
		}
		// Inverse of the encoding applied to $ref pointer segments in to-json-schema.ts. Per RFC 6901 the `~1` replacement must run before `~0`.
		function decodeJSONPointerSegment(segment) {
		    return segment.replace(/~1/g, "/").replace(/~0/g, "~");
		}
		function resolveRef(ref, ctx) {
		    if (!ref.startsWith("#")) {
		        throw new Error("External $ref is not supported, only local refs (#/...) are allowed");
		    }
		    const path = ref.slice(1).split("/").filter(Boolean);
		    // Handle root reference "#"
		    if (path.length === 0) {
		        return ctx.rootSchema;
		    }
		    const defsKey = ctx.version === "draft-2020-12" ? "$defs" : "definitions";
		    if (path[0] === defsKey) {
		        const key = path[1] === undefined ? undefined : decodeJSONPointerSegment(path[1]);
		        if (!key || !ctx.defs[key]) {
		            throw new Error(`Reference not found: ${ref}`);
		        }
		        return ctx.defs[key];
		    }
		    throw new Error(`Reference not found: ${ref}`);
		}
		/**
		 * Enforces the keywords that constrain an object's own keys — `propertyNames`,
		 * `minProperties`, `maxProperties` — before `objectSchema` runs. The guard has
		 * to see the raw input: an object parse drops `__proto__` and can add keys from
		 * a property `default`, so its output is not the set of names the instance
		 * actually carried.
		 */
		function checkObjectGuards(objectSchema, guards) {
		    // An identity transform, not z.any(), so `toJSONSchema` reports the object on both the input and the output side of the pipe.
		    const guard = z
		        .transform((value) => value)
		        .check((payload) => {
		        const value = payload.value;
		        if (typeof value !== "object" || value === null || Array.isArray(value))
		            return;
		        const keys = Object.getOwnPropertyNames(value);
		        if (guards.minProperties !== undefined && keys.length < guards.minProperties) {
		            payload.issues.push({
		                origin: "object",
		                code: "too_small",
		                minimum: guards.minProperties,
		                inclusive: true,
		                message: `Too small: expected object to have >=${guards.minProperties} properties`,
		                input: value,
		                inst: objectSchema,
		                continue: true,
		            });
		        }
		        if (guards.maxProperties !== undefined && keys.length > guards.maxProperties) {
		            payload.issues.push({
		                origin: "object",
		                code: "too_big",
		                maximum: guards.maxProperties,
		                inclusive: true,
		                message: `Too big: expected object to have <=${guards.maxProperties} properties`,
		                input: value,
		                inst: objectSchema,
		                continue: true,
		            });
		        }
		        if (guards.keySchema) {
		            for (const key of keys) {
		                const result = guards.keySchema.safeParse(key);
		                if (result.success)
		                    continue;
		                payload.issues.push({
		                    code: "invalid_key",
		                    origin: "record",
		                    issues: result.error.issues,
		                    input: key,
		                    path: [key],
		                    continue: true,
		                });
		            }
		        }
		    });
		    return guard.pipe(objectSchema);
		}
		/**
		 * An injective string for JSON data: two values share a key exactly when `uniqueItems` calls them
		 * equal, so duplicate detection is a Map lookup rather than a pairwise walk. Strings and keys are
		 * length-prefixed so no value can spell another one. `null` means "never equal to anything" — a
		 * cycle or a NaN, neither of which JSON can express.
		 */
		function canonicalKey(value, seen) {
		    if (value === null)
		        return "z";
		    const type = typeof value;
		    if (type !== "object") {
		        if (type === "number" && Number.isNaN(value))
		            return null;
		        const raw = String(value);
		        return `${type[0]}${raw.length}:${raw}`;
		    }
		    if (seen.has(value))
		        return null;
		    seen.add(value);
		    try {
		        if (Array.isArray(value)) {
		            const parts = [];
		            for (const item of value) {
		                const key = canonicalKey(item, seen);
		                if (key === null)
		                    return null;
		                parts.push(key);
		            }
		            return `a${parts.length}:[${parts.join(",")}]`;
		        }
		        const keys = Object.keys(value).sort();
		        const parts = [];
		        for (const k of keys) {
		            const key = canonicalKey(value[k], seen);
		            if (key === null)
		                return null;
		            parts.push(`${k.length}:${k}=${key}`);
		        }
		        return `o${parts.length}:{${parts.join(",")}}`;
		    }
		    finally {
		        seen.delete(value);
		    }
		}
		// keywords whose value is a schema or an array of them
		const SCHEMA_KEYWORDS = /*@__PURE__*/ new Set([
		    "items",
		    "prefixItems",
		    "additionalItems",
		    "additionalProperties",
		    "contains",
		    "propertyNames",
		    "not",
		    "if",
		    "then",
		    "else",
		    "allOf",
		    "anyOf",
		    "oneOf",
		    "unevaluatedItems",
		    "unevaluatedProperties",
		    "contentSchema",
		]);
		// keywords whose value maps names to schemas. draft-7 `dependencies` also allows an array of property names, which holds no schema to walk
		const SCHEMA_MAP_KEYWORDS = /*@__PURE__*/ new Set([
		    "properties",
		    "patternProperties",
		    "dependentSchemas",
		    "dependencies",
		    "$defs",
		    "definitions",
		]);
		/**
		 * True when a subschema holds a `$ref` anywhere a schema can appear. Only those positions are
		 * walked: `$ref` under `default` or an `x-` annotation is instance data with an ordinary key, and
		 * reading it as a reference would drop a constraint that resolves perfectly well.
		 */
		function containsRef(value) {
		    if (typeof value !== "object" || value === null)
		        return false;
		    if (Array.isArray(value))
		        return value.some(containsRef);
		    if (typeof value.$ref === "string")
		        return true;
		    return Object.entries(value).some(([key, sub]) => {
		        if (SCHEMA_KEYWORDS.has(key))
		            return containsRef(sub);
		        if (!SCHEMA_MAP_KEYWORDS.has(key) || typeof sub !== "object" || sub === null)
		            return false;
		        return Object.values(sub).some(containsRef);
		    });
		}
		function plural(n) {
		    return n === 1 ? "element" : "elements";
		}
		/**
		 * Enforces `uniqueItems` and the `contains` family before `arraySchema` runs, for the same reason
		 * `checkObjectGuards` runs pre-pipe: an item parse can apply a nested `default`, so the parsed
		 * array is not the instance the keywords are defined over. A guard issue aborts the pipe, so
		 * `minItems`/`maxItems` are not also reported on the same parse — instance correctness is worth
		 * more than co-reporting two issues.
		 */
		function checkArrayGuards(arraySchema, guards) {
		    // An identity transform, not z.any(), so `toJSONSchema` reports the array on both the input and the output side of the pipe.
		    const guard = z
		        .transform((value) => value)
		        .check((payload) => {
		        const items = payload.value;
		        if (!Array.isArray(items))
		            return;
		        if (guards.uniqueItems === true) {
		            const firstSeen = new Map();
		            for (let i = 0; i < items.length; i++) {
		                const key = canonicalKey(items[i], new Set());
		                if (key === null)
		                    continue;
		                const first = firstSeen.get(key);
		                if (first === undefined) {
		                    firstSeen.set(key, i);
		                    continue;
		                }
		                payload.issues.push({
		                    code: "custom",
		                    message: `Array items must be unique: element at index ${i} duplicates the one at index ${first}`,
		                    input: items,
		                    path: [i],
		                    continue: true,
		                });
		            }
		        }
		        if (guards.containsSchema) {
		            const minContains = guards.minContains ?? 1;
		            // one past the ceiling already proves the failure, and nothing above it changes the verdict
		            const ceiling = guards.maxContains !== undefined ? guards.maxContains + 1 : Number.POSITIVE_INFINITY;
		            let matches = 0;
		            for (const item of items) {
		                if (guards.containsSchema.safeParse(item).success && ++matches >= ceiling)
		                    break;
		            }
		            if (matches < minContains) {
		                // the scan ran to the end, so this count is exact
		                payload.issues.push({
		                    code: "custom",
		                    message: `Array must contain at least ${minContains} matching ${plural(minContains)}; found ${matches}`,
		                    input: items,
		                    continue: true,
		                });
		            }
		            if (guards.maxContains !== undefined && matches > guards.maxContains) {
		                payload.issues.push({
		                    code: "custom",
		                    message: `Array must contain at most ${guards.maxContains} matching ${plural(guards.maxContains)}`,
		                    input: items,
		                    continue: true,
		                });
		            }
		        }
		    });
		    return guard.pipe(arraySchema);
		}
		function getTupleRest(restSchema, ctx) {
		    if (restSchema === false) {
		        return undefined;
		    }
		    if (restSchema === undefined || restSchema === true) {
		        return z.any();
		    }
		    return convertSchema(restSchema, ctx);
		}
		// the RFC 3339 full-time keyword, narrower than `z.iso.time()`; local because sharing it via `timeSource` pins that builder into every bundle (+139 B gzipped on a mini `z.boolean()`)
		const fullTime = /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;
		function convertBaseSchema(schema, ctx) {
		    // Handle unsupported features
		    if (schema.not !== undefined) {
		        // Special case: { not: {} } represents never
		        if (typeof schema.not === "object" && Object.keys(schema.not).length === 0) {
		            return z.never();
		        }
		        throw new Error("not is not supported in Zod (except { not: {} } for never)");
		    }
		    if (schema.unevaluatedItems !== undefined) {
		        throw new Error("unevaluatedItems is not supported");
		    }
		    if (schema.unevaluatedProperties !== undefined) {
		        throw new Error("unevaluatedProperties is not supported");
		    }
		    if (schema.if !== undefined || schema.then !== undefined || schema.else !== undefined) {
		        throw new Error("Conditional schemas (if/then/else) are not supported");
		    }
		    if (schema.dependentSchemas !== undefined || schema.dependentRequired !== undefined) {
		        throw new Error("dependentSchemas and dependentRequired are not supported");
		    }
		    // Handle $ref
		    if (schema.$ref) {
		        const refPath = schema.$ref;
		        if (ctx.refs.has(refPath)) {
		            return ctx.refs.get(refPath);
		        }
		        if (ctx.processing.has(refPath)) {
		            // Circular reference - use lazy
		            return z.lazy(() => {
		                if (!ctx.refs.has(refPath)) {
		                    throw new Error(`Circular reference not resolved: ${refPath}`);
		                }
		                return ctx.refs.get(refPath);
		            });
		        }
		        ctx.processing.add(refPath);
		        const resolved = resolveRef(refPath, ctx);
		        const zodSchema = convertSchema(resolved, ctx);
		        ctx.refs.set(refPath, zodSchema);
		        ctx.processing.delete(refPath);
		        return zodSchema;
		    }
		    // Handle enum
		    if (schema.enum !== undefined) {
		        const enumValues = schema.enum;
		        // Special case: OpenAPI 3.0 null representation { type: "string", nullable: true, enum: [null] }
		        if (ctx.version === "openapi-3.0" &&
		            schema.nullable === true &&
		            enumValues.length === 1 &&
		            enumValues[0] === null) {
		            return z.null();
		        }
		        if (enumValues.length === 0) {
		            return z.never();
		        }
		        if (enumValues.length === 1) {
		            return z.literal(enumValues[0]);
		        }
		        // Check if all values are strings
		        if (enumValues.every((v) => typeof v === "string")) {
		            return z.enum(enumValues);
		        }
		        // Mixed types - use union of literals
		        const literalSchemas = enumValues.map((v) => z.literal(v));
		        if (literalSchemas.length < 2) {
		            return literalSchemas[0];
		        }
		        return z.union([literalSchemas[0], literalSchemas[1], ...literalSchemas.slice(2)]);
		    }
		    // Handle const
		    if (schema.const !== undefined) {
		        return z.literal(schema.const);
		    }
		    // Handle type
		    const type = schema.type;
		    if (Array.isArray(type)) {
		        // Expand type array into anyOf union
		        const typeSchemas = type.map((t) => {
		            const typeSchema = { ...schema, type: t };
		            return convertBaseSchema(typeSchema, ctx);
		        });
		        if (typeSchemas.length === 0) {
		            return z.never();
		        }
		        if (typeSchemas.length === 1) {
		            return typeSchemas[0];
		        }
		        return z.union(typeSchemas);
		    }
		    if (!type) {
		        // No type specified - empty schema (any)
		        return z.any();
		    }
		    let zodSchema;
		    switch (type) {
		        case "string": {
		            let stringSchema = z.string();
		            // Apply format using .check() with Zod format functions
		            if (schema.format) {
		                const format = schema.format;
		                // Map common formats to Zod check functions
		                if (format === "email") {
		                    stringSchema = stringSchema.check(z.email());
		                }
		                else if (format === "uri" || format === "uri-reference") {
		                    stringSchema = stringSchema.check(z.url());
		                }
		                else if (format === "uuid" || format === "guid") {
		                    stringSchema = stringSchema.check(z.uuid());
		                }
		                else if (format === "date-time") {
		                    stringSchema = stringSchema.check(z.iso.datetime({ offset: true }));
		                }
		                else if (format === "date") {
		                    stringSchema = stringSchema.check(z.iso.date());
		                }
		                else if (format === "time") {
		                    stringSchema = stringSchema.check(z.regex(fullTime));
		                }
		                else if (format === "duration") {
		                    stringSchema = stringSchema.check(z.iso.duration());
		                }
		                else if (format === "hostname") {
		                    stringSchema = stringSchema.check(z.hostname());
		                }
		                else if (format === "ipv4") {
		                    stringSchema = stringSchema.check(z.ipv4());
		                }
		                else if (format === "ipv6") {
		                    stringSchema = stringSchema.check(z.ipv6());
		                }
		                else if (format === "mac") {
		                    stringSchema = stringSchema.check(z.mac());
		                }
		                else if (format === "cidr") {
		                    stringSchema = stringSchema.check(z.cidrv4());
		                }
		                else if (format === "cidr-v6") {
		                    stringSchema = stringSchema.check(z.cidrv6());
		                }
		                else if (format === "base64") {
		                    stringSchema = stringSchema.check(z.base64());
		                }
		                else if (format === "base64url") {
		                    stringSchema = stringSchema.check(z.base64url());
		                }
		                else if (format === "e164") {
		                    stringSchema = stringSchema.check(z.e164());
		                }
		                else if (format === "credit_card") {
		                    stringSchema = stringSchema.check(z.creditCard());
		                }
		                else if (format === "iban") {
		                    stringSchema = stringSchema.check(z.iban());
		                }
		                else if (format === "jwt") {
		                    stringSchema = stringSchema.check(z.jwt());
		                }
		                else if (format === "emoji") {
		                    stringSchema = stringSchema.check(z.emoji());
		                }
		                else if (format === "nanoid") {
		                    stringSchema = stringSchema.check(z.nanoid());
		                }
		                else if (format === "cuid") {
		                    stringSchema = stringSchema.check(z.cuid());
		                }
		                else if (format === "cuid2") {
		                    stringSchema = stringSchema.check(z.cuid2());
		                }
		                else if (format === "ulid") {
		                    stringSchema = stringSchema.check(z.ulid());
		                }
		                else if (format === "xid") {
		                    stringSchema = stringSchema.check(z.xid());
		                }
		                else if (format === "ksuid") {
		                    stringSchema = stringSchema.check(z.ksuid());
		                }
		                // Note: json-string format is not currently supported by Zod
		                // Custom formats are ignored - keep as plain string
		            }
		            // Apply constraints
		            if (typeof schema.minLength === "number") {
		                stringSchema = stringSchema.min(schema.minLength);
		            }
		            if (typeof schema.maxLength === "number") {
		                stringSchema = stringSchema.max(schema.maxLength);
		            }
		            if (schema.pattern) {
		                // JSON Schema patterns are not implicitly anchored (match anywhere in string)
		                stringSchema = stringSchema.regex(new RegExp(schema.pattern));
		            }
		            zodSchema = stringSchema;
		            break;
		        }
		        case "number":
		        case "integer": {
		            let numberSchema = type === "integer" ? z.number().int() : z.number();
		            // Apply constraints
		            // In draft-04, `exclusiveMinimum: true` makes the sibling `minimum` exclusive rather than an independent bound, so the inclusive `.min()` is skipped; emitting it too would be dominated by the exclusive check and report a second, weaker issue.
		            if (typeof schema.minimum === "number" && schema.exclusiveMinimum !== true) {
		                numberSchema = numberSchema.min(schema.minimum);
		            }
		            if (typeof schema.maximum === "number" && schema.exclusiveMaximum !== true) {
		                numberSchema = numberSchema.max(schema.maximum);
		            }
		            if (typeof schema.exclusiveMinimum === "number") {
		                numberSchema = numberSchema.gt(schema.exclusiveMinimum);
		            }
		            else if (schema.exclusiveMinimum === true && typeof schema.minimum === "number") {
		                numberSchema = numberSchema.gt(schema.minimum);
		            }
		            if (typeof schema.exclusiveMaximum === "number") {
		                numberSchema = numberSchema.lt(schema.exclusiveMaximum);
		            }
		            else if (schema.exclusiveMaximum === true && typeof schema.maximum === "number") {
		                numberSchema = numberSchema.lt(schema.maximum);
		            }
		            if (typeof schema.multipleOf === "number") {
		                numberSchema = numberSchema.multipleOf(schema.multipleOf);
		            }
		            zodSchema = numberSchema;
		            break;
		        }
		        case "boolean": {
		            zodSchema = z.boolean();
		            break;
		        }
		        case "null": {
		            zodSchema = z.null();
		            break;
		        }
		        case "object": {
		            const shape = {};
		            const properties = schema.properties || {};
		            const requiredSet = new Set(schema.required || []);
		            const additionalSchema = typeof schema.additionalProperties === "object"
		                ? convertSchema(schema.additionalProperties, ctx)
		                : undefined;
		            // Convert properties - mark optional ones
		            for (const [key, propSchema] of Object.entries(properties)) {
		                const propZodSchema = convertSchema(propSchema, ctx);
		                // If not in required array, make it optional. assignProp so a __proto__ key becomes an own property instead of hitting the inherited setter
		                (0, util_js_1.assignProp)(shape, key, requiredSet.has(key) ? propZodSchema : propZodSchema.optional());
		            }
		            // Handle patternProperties
		            if (schema.patternProperties) {
		                // patternProperties: keys matching pattern must satisfy corresponding schema. Use loose records so non-matching keys pass through
		                const patternProps = schema.patternProperties;
		                const patternKeys = Object.keys(patternProps);
		                const looseRecords = [];
		                for (const pattern of patternKeys) {
		                    const patternValue = convertSchema(patternProps[pattern], ctx);
		                    const keySchema = z.string().regex(new RegExp(pattern));
		                    looseRecords.push(z.looseRecord(keySchema, patternValue));
		                }
		                // Build intersection: object schema + all pattern property records
		                const schemasToIntersect = [];
		                if (Object.keys(shape).length > 0) {
		                    // Use passthrough so patternProperties can validate additional keys
		                    schemasToIntersect.push(z.object(shape).passthrough());
		                }
		                schemasToIntersect.push(...looseRecords);
		                if (schemasToIntersect.length === 0) {
		                    zodSchema = z.object({}).passthrough();
		                }
		                else if (schemasToIntersect.length === 1) {
		                    zodSchema = schemasToIntersect[0];
		                }
		                else {
		                    // Chain intersections: (A & B) & C & D ...
		                    let result = z.intersection(schemasToIntersect[0], schemasToIntersect[1]);
		                    for (let i = 2; i < schemasToIntersect.length; i++) {
		                        result = z.intersection(result, schemasToIntersect[i]);
		                    }
		                    zodSchema = result;
		                }
		                // When additionalProperties is false, reject keys that are neither defined in properties nor matched by any patternProperty.
		                if (schema.additionalProperties === false) {
		                    const propertyKeys = Object.keys(shape);
		                    const patterns = patternKeys.map((p) => new RegExp(p));
		                    const basePatternSchema = zodSchema;
		                    zodSchema = zodSchema.check((payload) => {
		                        if (!(0, util_js_1.isPlainObject)(payload.value))
		                            return;
		                        const unrecognized = [];
		                        for (const key of Object.keys(payload.value)) {
		                            if (propertyKeys.includes(key))
		                                continue;
		                            if (patterns.some((regex) => regex.test(key)))
		                                continue;
		                            unrecognized.push(key);
		                        }
		                        if (unrecognized.length) {
		                            payload.issues.push({
		                                code: "unrecognized_keys",
		                                keys: unrecognized,
		                                input: payload.value,
		                                inst: basePatternSchema,
		                            });
		                        }
		                    });
		                }
		            }
		            else {
		                // Handle additionalProperties. In JSON Schema, additionalProperties defaults to true (allow any extra properties). In Zod, objects strip unknown keys by default, so we need to handle this explicitly
		                const objectSchema = z.object(shape);
		                if (schema.additionalProperties === false) {
		                    // Strict mode - no extra properties allowed
		                    zodSchema = objectSchema.strict();
		                }
		                else if (additionalSchema) {
		                    // Extra properties must match the specified schema
		                    zodSchema = objectSchema.catchall(additionalSchema);
		                }
		                else {
		                    // additionalProperties is true or undefined - allow any extra properties (passthrough)
		                    zodSchema = objectSchema.passthrough();
		                }
		            }
		            // propertyNames constrains key *names* only, and says nothing about which keys are required or how their values validate. Layering it on top of the result keeps properties/patternProperties/additionalProperties composing underneath. `true` allows every name, so it needs no guard.
		            const hasKeyGuard = schema.propertyNames !== undefined && schema.propertyNames !== true;
		            const minProperties = typeof schema.minProperties === "number" ? schema.minProperties : undefined;
		            const maxProperties = typeof schema.maxProperties === "number" ? schema.maxProperties : undefined;
		            if (hasKeyGuard || minProperties !== undefined || maxProperties !== undefined) {
		                let keySchema;
		                if (hasKeyGuard) {
		                    // Keys are always strings, so a propertyNames subschema that omits `type` still constrains them — without this it would convert to z.any().
		                    const keyJSONSchema = typeof schema.propertyNames === "object" && schema.propertyNames.type === undefined
		                        ? { type: "string", ...schema.propertyNames }
		                        : schema.propertyNames;
		                    keySchema = convertSchema(keyJSONSchema, ctx);
		                }
		                zodSchema = checkObjectGuards(zodSchema, { keySchema, minProperties, maxProperties });
		            }
		            break;
		        }
		        case "array": {
		            // Check if this is a tuple (prefixItems or items as array)
		            const prefixItems = schema.prefixItems;
		            const items = schema.items;
		            if (prefixItems && Array.isArray(prefixItems)) {
		                // Tuple with prefixItems (draft-2020-12)
		                const minItems = typeof schema.minItems === "number" ? schema.minItems : 0;
		                const tupleItems = prefixItems.map((item) => convertSchema(item, ctx));
		                const positionalItems = applyMinItems(tupleItems, minItems);
		                const rest = !Array.isArray(items) ? getTupleRest(items, ctx) : undefined;
		                const tupleSchema = z.tuple(positionalItems);
		                zodSchema = rest ? tupleSchema.rest(rest) : tupleSchema;
		                // Apply minItems/maxItems constraints to tuples
		                if (typeof schema.minItems === "number") {
		                    zodSchema = zodSchema.check(z.minLength(schema.minItems));
		                }
		                if (typeof schema.maxItems === "number") {
		                    zodSchema = zodSchema.check(z.maxLength(schema.maxItems));
		                }
		            }
		            else if (Array.isArray(items)) {
		                // Tuple with items array (draft-7)
		                const minItems = typeof schema.minItems === "number" ? schema.minItems : 0;
		                const tupleItems = items.map((item) => convertSchema(item, ctx));
		                const positionalItems = applyMinItems(tupleItems, minItems);
		                const rest = getTupleRest(schema.additionalItems, ctx);
		                const tupleSchema = z.tuple(positionalItems);
		                zodSchema = rest ? tupleSchema.rest(rest) : tupleSchema;
		                // Apply minItems/maxItems constraints to tuples
		                if (typeof schema.minItems === "number") {
		                    zodSchema = zodSchema.check(z.minLength(schema.minItems));
		                }
		                if (typeof schema.maxItems === "number") {
		                    zodSchema = zodSchema.check(z.maxLength(schema.maxItems));
		                }
		            }
		            else if (items !== undefined) {
		                // Regular array
		                const element = convertSchema(items, ctx);
		                let arraySchema = z.array(element);
		                // Apply constraints
		                if (typeof schema.minItems === "number") {
		                    arraySchema = arraySchema.min(schema.minItems);
		                }
		                if (typeof schema.maxItems === "number") {
		                    arraySchema = arraySchema.max(schema.maxItems);
		                }
		                zodSchema = arraySchema;
		            }
		            else {
		                // No items specified - array of any
		                zodSchema = z.array(z.any());
		            }
		            // minContains/maxContains only constrain anything when `contains` itself is present
		            if (schema.uniqueItems === true || schema.contains !== undefined) {
		                zodSchema = checkArrayGuards(zodSchema, {
		                    uniqueItems: schema.uniqueItems === true,
		                    containsSchema: schema.contains !== undefined ? convertSchema(schema.contains, ctx) : undefined,
		                    minContains: typeof schema.minContains === "number" ? schema.minContains : undefined,
		                    maxContains: typeof schema.maxContains === "number" ? schema.maxContains : undefined,
		                });
		            }
		            break;
		        }
		        default:
		            throw new Error(`Unsupported type: ${type}`);
		    }
		    return zodSchema;
		}
		function convertSchema(schema, ctx) {
		    if (typeof schema === "boolean") {
		        return schema ? z.any() : z.never();
		    }
		    // Convert base schema first (ignoring composition keywords)
		    let baseSchema = convertBaseSchema(schema, ctx);
		    const hasExplicitType = schema.type || schema.enum !== undefined || schema.const !== undefined;
		    // Process composition keywords LAST (they can appear together)
		    // Handle anyOf - wrap base schema with union
		    if (schema.anyOf && Array.isArray(schema.anyOf)) {
		        const options = schema.anyOf.map((s) => convertSchema(s, ctx));
		        const anyOfUnion = z.union(options);
		        baseSchema = hasExplicitType ? z.intersection(baseSchema, anyOfUnion) : anyOfUnion;
		    }
		    // Handle oneOf - exclusive union (exactly one must match)
		    if (schema.oneOf && Array.isArray(schema.oneOf)) {
		        const options = schema.oneOf.map((s) => convertSchema(s, ctx));
		        const oneOfUnion = z.xor(options);
		        baseSchema = hasExplicitType ? z.intersection(baseSchema, oneOfUnion) : oneOfUnion;
		    }
		    // Handle allOf - wrap base schema with intersection
		    if (schema.allOf && Array.isArray(schema.allOf)) {
		        if (schema.allOf.length === 0) {
		            baseSchema = hasExplicitType ? baseSchema : z.any();
		        }
		        else {
		            let result = hasExplicitType ? baseSchema : convertSchema(schema.allOf[0], ctx);
		            const startIdx = hasExplicitType ? 0 : 1;
		            for (let i = startIdx; i < schema.allOf.length; i++) {
		                result = z.intersection(result, convertSchema(schema.allOf[i], ctx));
		            }
		            baseSchema = result;
		        }
		    }
		    // Handle nullable (OpenAPI 3.0)
		    if (schema.nullable === true && ctx.version === "openapi-3.0") {
		        baseSchema = z.nullable(baseSchema);
		    }
		    // Handle readOnly
		    if (schema.readOnly === true) {
		        baseSchema = z.readonly(baseSchema);
		    }
		    // Apply `default` so it wraps the fully-composed schema. This ensures `parse(undefined) -> default` works regardless of which branch of `convertBaseSchema` produced the inner schema (enum/const/not/typed/etc.).
		    if (schema.default !== undefined) {
		        baseSchema = baseSchema.default(schema.default);
		    }
		    // Collect non-description annotation metadata into the user-supplied registry. Description is handled separately below via `.describe()` to preserve the contract that `schema.description` reads from globalRegistry.
		    const extraMeta = {};
		    const coreMetadataKeys = ["$id", "id", "$comment", "$anchor", "$vocabulary", "$dynamicRef", "$dynamicAnchor"];
		    for (const key of coreMetadataKeys) {
		        if (key in schema) {
		            extraMeta[key] = schema[key];
		        }
		    }
		    const contentMetadataKeys = ["contentEncoding", "contentMediaType", "contentSchema"];
		    for (const key of contentMetadataKeys) {
		        if (key in schema) {
		            extraMeta[key] = schema[key];
		        }
		    }
		    // `propertyNames` and `contains` are enforced by a guard, which `toJSONSchema` cannot infer, so the original keyword is carried as metadata to keep the round-trip lossless. Only where it was actually applied: on any other type it is inert, and on a `$ref` the metadata would land on the target every reference shares. A carried subschema is copied verbatim while the root `$defs` stay behind, so one holding a `$ref` is dropped rather than emitted as a dangling reference.
		    if (schema.type === "object" && schema.$ref === undefined) {
		        if (schema.propertyNames !== undefined && !containsRef(schema.propertyNames)) {
		            extraMeta.propertyNames = schema.propertyNames;
		        }
		        for (const key of ["minProperties", "maxProperties"]) {
		            if (schema[key] !== undefined)
		                extraMeta[key] = schema[key];
		        }
		    }
		    if (schema.type === "array" && schema.$ref === undefined) {
		        if (schema.contains !== undefined && !containsRef(schema.contains)) {
		            extraMeta.contains = schema.contains;
		        }
		        for (const key of ["uniqueItems", "minContains", "maxContains"]) {
		            if (schema[key] !== undefined)
		                extraMeta[key] = schema[key];
		        }
		    }
		    for (const key of Object.keys(schema)) {
		        if (!RECOGNIZED_KEYS.has(key)) {
		            (0, util_js_1.assignProp)(extraMeta, key, schema[key]);
		        }
		    }
		    if (Object.keys(extraMeta).length > 0) {
		        ctx.registry.add(baseSchema, extraMeta);
		    }
		    // Apply description last. `.describe()` clones the schema and sets `_zod.parent` on the clone, so registry lookups on the returned reference still resolve `extraMeta` via parent inheritance.
		    if (schema.description) {
		        baseSchema = baseSchema.describe(schema.description);
		    }
		    return baseSchema;
		}
		/**
		 * Converts a JSON Schema to a Zod schema. This function should be considered semi-experimental. It's behavior is liable to change. */
		function fromJSONSchema(schema, params) {
		    // Handle boolean schemas
		    if (typeof schema === "boolean") {
		        return schema ? z.any() : z.never();
		    }
		    // Normalize input via a JSON round-trip. This guarantees the converter walks a plain, finite, JSON-valid object graph: cyclic inputs fail here, getter/Proxy-based properties are materialized into static values, and class instances collapse to plain objects.
		    let normalized;
		    try {
		        normalized = JSON.parse(JSON.stringify(schema));
		    }
		    catch {
		        throw new Error("fromJSONSchema input is not valid JSON (possibly cyclic); use $defs/$ref for recursive schemas");
		    }
		    const version = detectVersion(normalized, params?.defaultTarget);
		    const defs = (normalized.$defs || normalized.definitions || {});
		    const ctx = {
		        version,
		        defs,
		        refs: new Map(),
		        processing: new Set(),
		        rootSchema: normalized,
		        registry: params?.registry ?? registries_js_1.globalRegistry,
		    };
		    return convertSchema(normalized, ctx);
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (fromJsonSchema));
	return fromJsonSchema;
}

var deepPartial = {};

var visit = {};

var hasRequiredVisit;

function requireVisit () {
	if (hasRequiredVisit) return visit;
	hasRequiredVisit = 1;
	(function (exports) {
		// Traversal pattern adapted from Jaen's v3 `mapOnSchema` (Apache-2.0): https://gist.github.com/jaens/7e15ae1984bb338c86eb5e452dee3010
		var __createBinding = (visit && visit.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (visit && visit.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (visit && visit.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.visit = visit$1;
		const schemas = __importStar(/*@__PURE__*/ requireSchemas$1());
		const util_js_1 = /*@__PURE__*/ requireUtil$2();
		const RESOLVING = Symbol("z.visit/resolving");
		function visit$1(schema, fnOrHandlers) {
		    const fn = typeof fnOrHandlers === "function"
		        ? fnOrHandlers
		        : (node, rewritten) => {
		            // A union of handlers isn't callable with one argument; handler `K` only ever sees kind `K`.
		            const h = fnOrHandlers[node._zod.def.type];
		            return h ? h(node, rewritten) : node;
		        };
		    const cache = new Map();
		    function run(s) {
		        const cached = cache.get(s);
		        if (cached === RESOLVING) {
		            // Non-lazy cycle. Defer to parse time, when the cache holds the finished node.
		            return new schemas.$ZodLazy({
		                type: "lazy",
		                getter: () => cache.get(s),
		            });
		        }
		        if (cached !== undefined)
		            return cached;
		        cache.set(s, RESOLVING);
		        const inner = mapInner(s);
		        const mapped = fn(inner, inner !== s);
		        cache.set(s, mapped);
		        return mapped;
		    }
		    function mapInner(s) {
		        const def = s._zod.def;
		        const kind = def.type;
		        switch (kind) {
		            case "object": {
		                const oldShape = def.shape;
		                const keys = Object.keys(oldShape);
		                let changed = false;
		                const newShape = {};
		                for (const k of keys) {
		                    const mapped = run(oldShape[k]);
		                    if (mapped !== oldShape[k])
		                        changed = true;
		                    newShape[k] = mapped;
		                }
		                let newCatchall = def.catchall;
		                if (def.catchall) {
		                    newCatchall = run(def.catchall);
		                    if (newCatchall !== def.catchall)
		                        changed = true;
		                }
		                return changed ? (0, util_js_1.clone)(s, { ...def, shape: newShape, catchall: newCatchall }) : s;
		            }
		            case "array": {
		                const mapped = run(def.element);
		                return mapped === def.element ? s : (0, util_js_1.clone)(s, { ...def, element: mapped });
		            }
		            case "tuple": {
		                const oldItems = def.items;
		                let changed = false;
		                const newItems = [];
		                for (const item of oldItems) {
		                    const mapped = run(item);
		                    if (mapped !== item)
		                        changed = true;
		                    newItems.push(mapped);
		                }
		                let newRest = def.rest;
		                if (def.rest) {
		                    newRest = run(def.rest);
		                    if (newRest !== def.rest)
		                        changed = true;
		                }
		                return changed ? (0, util_js_1.clone)(s, { ...def, items: newItems, rest: newRest }) : s;
		            }
		            case "record":
		            case "map": {
		                const newKey = run(def.keyType);
		                const newVal = run(def.valueType);
		                return newKey === def.keyType && newVal === def.valueType
		                    ? s
		                    : (0, util_js_1.clone)(s, { ...def, keyType: newKey, valueType: newVal });
		            }
		            case "set": {
		                const newVal = run(def.valueType);
		                return newVal === def.valueType ? s : (0, util_js_1.clone)(s, { ...def, valueType: newVal });
		            }
		            case "union": {
		                const oldOptions = def.options;
		                let changed = false;
		                const newOptions = [];
		                for (const opt of oldOptions) {
		                    const mapped = run(opt);
		                    if (mapped !== opt)
		                        changed = true;
		                    newOptions.push(mapped);
		                }
		                return changed ? (0, util_js_1.clone)(s, { ...def, options: newOptions }) : s;
		            }
		            case "intersection": {
		                const newLeft = run(def.left);
		                const newRight = run(def.right);
		                return newLeft === def.left && newRight === def.right
		                    ? s
		                    : (0, util_js_1.clone)(s, { ...def, left: newLeft, right: newRight });
		            }
		            case "optional":
		            case "nullable":
		            case "default":
		            case "prefault":
		            case "catch":
		            case "readonly":
		            case "nonoptional":
		            case "promise":
		            case "success": {
		                const newInner = run(def.innerType);
		                return newInner === def.innerType ? s : (0, util_js_1.clone)(s, { ...def, innerType: newInner });
		            }
		            case "pipe": {
		                const newIn = run(def.in);
		                const newOut = run(def.out);
		                return newIn === def.in && newOut === def.out ? s : (0, util_js_1.clone)(s, { ...def, in: newIn, out: newOut });
		            }
		            case "function": {
		                const newInput = run(def.input);
		                const newOutput = run(def.output);
		                return newInput === def.input && newOutput === def.output
		                    ? s
		                    : (0, util_js_1.clone)(s, { ...def, input: newInput, output: newOutput });
		            }
		            case "lazy": {
		                // Invoking the getter here would trip the cycle check, so lazy nodes always re-clone.
		                const original = def.getter;
		                // Drop the memo, or it shadows the new getter forever.
		                const { _cachedInner, ...rest } = def;
		                return (0, util_js_1.clone)(s, { ...rest, getter: () => run(original()) });
		            }
		            // A leaf by choice: `parts` are regex fragments, not data positions.
		            case "template_literal":
		            // Leaves.
		            case "string":
		            case "number":
		            case "int":
		            case "boolean":
		            case "bigint":
		            case "symbol":
		            case "undefined":
		            case "null":
		            case "void":
		            case "never":
		            case "any":
		            case "unknown":
		            case "date":
		            case "nan":
		            case "enum":
		            case "literal":
		            case "file":
		            case "transform":
		            case "custom":
		                return s;
		            default: {
		                return s;
		            }
		        }
		    }
		    return run(schema);
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (visit));
	return visit;
}

var hasRequiredDeepPartial;

function requireDeepPartial () {
	if (hasRequiredDeepPartial) return deepPartial;
	hasRequiredDeepPartial = 1;
	(function (exports) {
		var __createBinding = (deepPartial && deepPartial.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (deepPartial && deepPartial.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (deepPartial && deepPartial.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.deepPartial = deepPartial$1;
		const visit_js_1 = /*@__PURE__*/ requireVisit();
		const schemas = __importStar(/*@__PURE__*/ requireSchemas());
		/** Returns a copy of the schema with every nested object's properties made optional. */
		function deepPartial$1(schema) {
		    return (0, visit_js_1.visit)(schema, {
		        object: (s) => s.partial(),
		        // Every partialed option now admits `undefined`, which the constructor rejects as a duplicate.
		        union: (s) => {
		            const def = s._zod.def;
		            return def.discriminator === undefined ? s : schemas.union(def.options);
		        },
		    });
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (deepPartial));
	return deepPartial;
}

var inOut = {};

var hasRequiredInOut;

function requireInOut () {
	if (hasRequiredInOut) return inOut;
	hasRequiredInOut = 1;
	(function (exports) {
		var __createBinding = (inOut && inOut.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (inOut && inOut.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (inOut && inOut.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.input = input;
		exports.output = output;
		const util_js_1 = /*@__PURE__*/ requireUtil$2();
		const visit_js_1 = /*@__PURE__*/ requireVisit();
		const schemas = __importStar(/*@__PURE__*/ requireSchemas());
		/** Appends a pipe's own checks to the side that replaces it, mirroring how `.check()` clones. */
		function withChecks(side, checks) {
		    if (!checks?.length)
		        return side;
		    const def = side._zod.def;
		    return (0, util_js_1.clone)(side, (0, util_js_1.mergeDefs)(def, { checks: [...(def.checks ?? []), ...checks] }), { parent: true });
		}
		/** The out side, carrying the pipe's own checks: those run against the decoded value, which is what `out` produces. */
		function outSide(def) {
		    return withChecks(def.out, def.checks);
		}
		/** The in side. `z.preprocess` pipes a transform into a schema, and a bare transform validates nothing, so the schema it feeds is the real input side — the resolution `toJSONSchema` already makes for this case. */
		function inSide(def) {
		    return def.in._zod.traits.has("$ZodTransform") ? outSide(def) : def.in;
		}
		/** Returns a copy of the schema with every pipe replaced by its input side. A codec's checks are dropped: they constrain the decoded value the input side never produces. */
		function input(schema) {
		    return (0, visit_js_1.visit)(schema, {
		        pipe: (s) => inSide(s._zod.def),
		        // A default value belongs to the output side, so a rewritten inner type leaves it stranded. `.default()` widens the declared input type with `undefined`, and `optional` is what carries that across.
		        default: (s, rewritten) => (rewritten ? schemas.optional(s._zod.def.innerType) : s),
		        // A catch value is output-side too, but `.catch()` leaves the declared input type alone, so the inner schema stands on its own.
		        catch: (s, rewritten) => (rewritten ? s._zod.def.innerType : s),
		    });
		}
		/** Returns a copy of the schema with every pipe replaced by its output side, carrying over the pipe's own checks. */
		function output(schema) {
		    return (0, visit_js_1.visit)(schema, {
		        pipe: (s) => outSide(s._zod.def),
		        // A prefault value is fed through the schema, which makes it input-side, so a rewritten inner type leaves it stranded.
		        prefault: (s, rewritten) => (rewritten ? s._zod.def.innerType : s),
		    });
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (inOut));
	return inOut;
}

var coerce = {};

var hasRequiredCoerce;

function requireCoerce () {
	if (hasRequiredCoerce) return coerce;
	hasRequiredCoerce = 1;
	(function (exports) {
		var __createBinding = (coerce && coerce.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (coerce && coerce.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (coerce && coerce.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.string = string;
		exports.number = number;
		exports.boolean = boolean;
		exports.bigint = bigint;
		exports.date = date;
		const core = __importStar(/*@__PURE__*/ requireCore());
		const schemas = __importStar(/*@__PURE__*/ requireSchemas());
		function string(params) {
		    return core._coercedString(schemas.ZodString, params);
		}
		function number(params) {
		    return core._coercedNumber(schemas.ZodNumber, params);
		}
		function boolean(params) {
		    return core._coercedBoolean(schemas.ZodBoolean, params);
		}
		function bigint(params) {
		    return core._coercedBigint(schemas.ZodBigInt, params);
		}
		function date(params) {
		    return core._coercedDate(schemas.ZodDate, params);
		}

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (coerce));
	return coerce;
}

var hasRequiredExternal;

function requireExternal () {
	if (hasRequiredExternal) return external;
	hasRequiredExternal = 1;
	(function (exports) {
		var __createBinding = (external && external.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (external && external.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (external && external.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		var __exportStar = (external && external.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.coerce = exports.iso = exports.ZodISODuration = exports.ZodISOTime = exports.ZodISODate = exports.ZodISODateTime = exports.locales = exports.output = exports.input = exports.deepPartial = exports.fromJSONSchema = exports.toJSONSchema = exports.getDiscriminatedOption = exports.ZodCompileUnsupportedError = exports.ZodCompileAsyncError = exports.withParser = exports.compile = exports.toZod = exports.INVALID = exports.NEVER = exports.util = exports.TimePrecision = exports.flattenError = exports.formatError = exports.prettifyError = exports.treeifyError = exports.regexes = exports.clone = exports.$brand = exports.$input = exports.$output = exports.memoizer = exports.config = exports.registry = exports.globalRegistry = exports.core = void 0;
		exports.core = __importStar(/*@__PURE__*/ requireCore());
		__exportStar(/*@__PURE__*/ requireSchemas(), exports);
		__exportStar(/*@__PURE__*/ requireChecks(), exports);
		__exportStar(/*@__PURE__*/ requireErrors$2(), exports);
		__exportStar(/*@__PURE__*/ requireParse$3(), exports);
		__exportStar(/*@__PURE__*/ requireCompat(), exports);
		var index_js_1 = /*@__PURE__*/ requireCore();
		Object.defineProperty(exports, "globalRegistry", { enumerable: true, get: function () { return index_js_1.globalRegistry; } });
		Object.defineProperty(exports, "registry", { enumerable: true, get: function () { return index_js_1.registry; } });
		Object.defineProperty(exports, "config", { enumerable: true, get: function () { return index_js_1.config; } });
		Object.defineProperty(exports, "memoizer", { enumerable: true, get: function () { return index_js_1.memoizer; } });
		Object.defineProperty(exports, "$output", { enumerable: true, get: function () { return index_js_1.$output; } });
		Object.defineProperty(exports, "$input", { enumerable: true, get: function () { return index_js_1.$input; } });
		Object.defineProperty(exports, "$brand", { enumerable: true, get: function () { return index_js_1.$brand; } });
		Object.defineProperty(exports, "clone", { enumerable: true, get: function () { return index_js_1.clone; } });
		Object.defineProperty(exports, "regexes", { enumerable: true, get: function () { return index_js_1.regexes; } });
		Object.defineProperty(exports, "treeifyError", { enumerable: true, get: function () { return index_js_1.treeifyError; } });
		Object.defineProperty(exports, "prettifyError", { enumerable: true, get: function () { return index_js_1.prettifyError; } });
		Object.defineProperty(exports, "formatError", { enumerable: true, get: function () { return index_js_1.formatError; } });
		Object.defineProperty(exports, "flattenError", { enumerable: true, get: function () { return index_js_1.flattenError; } });
		Object.defineProperty(exports, "TimePrecision", { enumerable: true, get: function () { return index_js_1.TimePrecision; } });
		Object.defineProperty(exports, "util", { enumerable: true, get: function () { return index_js_1.util; } });
		Object.defineProperty(exports, "NEVER", { enumerable: true, get: function () { return index_js_1.NEVER; } });
		Object.defineProperty(exports, "INVALID", { enumerable: true, get: function () { return index_js_1.INVALID; } });
		Object.defineProperty(exports, "toZod", { enumerable: true, get: function () { return index_js_1.toZod; } });
		Object.defineProperty(exports, "compile", { enumerable: true, get: function () { return index_js_1.compile; } });
		Object.defineProperty(exports, "withParser", { enumerable: true, get: function () { return index_js_1.withParser; } });
		Object.defineProperty(exports, "ZodCompileAsyncError", { enumerable: true, get: function () { return index_js_1.ZodCompileAsyncError; } });
		Object.defineProperty(exports, "ZodCompileUnsupportedError", { enumerable: true, get: function () { return index_js_1.ZodCompileUnsupportedError; } });
		Object.defineProperty(exports, "getDiscriminatedOption", { enumerable: true, get: function () { return index_js_1.getDiscriminatedOption; } });
		var json_schema_processors_js_1 = /*@__PURE__*/ requireJsonSchemaProcessors();
		Object.defineProperty(exports, "toJSONSchema", { enumerable: true, get: function () { return json_schema_processors_js_1.toJSONSchema; } });
		var from_json_schema_js_1 = /*@__PURE__*/ requireFromJsonSchema();
		Object.defineProperty(exports, "fromJSONSchema", { enumerable: true, get: function () { return from_json_schema_js_1.fromJSONSchema; } });
		var deep_partial_js_1 = /*@__PURE__*/ requireDeepPartial();
		Object.defineProperty(exports, "deepPartial", { enumerable: true, get: function () { return deep_partial_js_1.deepPartial; } });
		// Types and values both, from one module — re-exporting the types from core would collide.
		var in_out_js_1 = /*@__PURE__*/ requireInOut();
		Object.defineProperty(exports, "input", { enumerable: true, get: function () { return in_out_js_1.input; } });
		Object.defineProperty(exports, "output", { enumerable: true, get: function () { return in_out_js_1.output; } });
		exports.locales = __importStar(/*@__PURE__*/ requireLocales());
		// iso must be exported from top-level https://github.com/colinhacks/zod/issues/4491
		var iso_js_1 = /*@__PURE__*/ requireIso();
		Object.defineProperty(exports, "ZodISODateTime", { enumerable: true, get: function () { return iso_js_1.ZodISODateTime; } });
		Object.defineProperty(exports, "ZodISODate", { enumerable: true, get: function () { return iso_js_1.ZodISODate; } });
		Object.defineProperty(exports, "ZodISOTime", { enumerable: true, get: function () { return iso_js_1.ZodISOTime; } });
		Object.defineProperty(exports, "ZodISODuration", { enumerable: true, get: function () { return iso_js_1.ZodISODuration; } });
		exports.iso = __importStar(/*@__PURE__*/ requireIso());
		exports.coerce = __importStar(/*@__PURE__*/ requireCoerce());

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (external));
	return external;
}

var hasRequiredZod;

function requireZod () {
	if (hasRequiredZod) return zod;
	hasRequiredZod = 1;
	(function (exports) {
		var __createBinding = (zod && zod.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (zod && zod.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (zod && zod.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		var __exportStar = (zod && zod.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.default = exports.z = void 0;
		const z = __importStar(/*@__PURE__*/ requireExternal());
		exports.z = z;
		exports.default = z;
		__exportStar(/*@__PURE__*/ requireExternal(), exports);

		// seal-cjs-exports
		(function () {
		  var keys = Object.getOwnPropertyNames(exports);
		  for (var i = 0; i < keys.length; i++) {
		    var desc = Object.getOwnPropertyDescriptor(exports, keys[i]);
		    if (!desc || !desc.get || !desc.configurable) continue;
		    var value;
		    try {
		      value = desc.get();
		    } catch (e) {
		      continue;
		    }
		    // a circular require may not have settled this one yet, so leave it live
		    if (value === undefined) continue;
		    Object.defineProperty(exports, keys[i], { value: value, writable: false, enumerable: desc.enumerable, configurable: false });
		  }
		  Object.freeze(exports);
		})(); 
	} (zod));
	return zod;
}

var hasRequiredZodPartial;

function requireZodPartial () {
	if (hasRequiredZodPartial) return zodPartial;
	hasRequiredZodPartial = 1;
	Object.defineProperty(zodPartial, "__esModule", { value: true });
	zodPartial.partialWithoutDefaults = partialWithoutDefaults;
	const zod_1 = /*@__PURE__*/ requireZod();
	function partialWithoutDefaults(schema) {
	    const shape = {};
	    for (const [key, field] of Object.entries(schema.shape)) {
	        const inner = field instanceof zod_1.z.ZodDefault || field instanceof zod_1.z.ZodPrefault ? field.unwrap() : field;
	        const description = field instanceof zod_1.z.ZodType ? field.description : undefined;
	        const optional = zod_1.z.optional(inner);
	        shape[key] = description === undefined ? optional : optional.describe(description);
	    }
	    const extended = schema.extend(shape);
	    const relaxed = schema.description === undefined ? extended : extended.describe(schema.description);
	    return relaxed;
	}
	
	return zodPartial;
}

var zodFieldPresence = {};

var hasRequiredZodFieldPresence;

function requireZodFieldPresence () {
	if (hasRequiredZodFieldPresence) return zodFieldPresence;
	hasRequiredZodFieldPresence = 1;
	Object.defineProperty(zodFieldPresence, "__esModule", { value: true });
	zodFieldPresence.allowingAbsentKey = allowingAbsentKey;
	zodFieldPresence.coercedString = coercedString;
	const zod_1 = /*@__PURE__*/ requireZod();
	function allowingAbsentKey(schema) {
	    const optional = schema.optional();
	    return optional;
	}
	function coercedString() {
	    return zod_1.z.preprocess((value) => String(value), zod_1.z.string());
	}
	
	return zodFieldPresence;
}

var compareExposedPad = {};

var hasRequiredCompareExposedPad;

function requireCompareExposedPad () {
	if (hasRequiredCompareExposedPad) return compareExposedPad;
	hasRequiredCompareExposedPad = 1;
	Object.defineProperty(compareExposedPad, "__esModule", { value: true });
	compareExposedPad.compareExposedPadPins = compareExposedPadPins;
	function compareExposedPadPins(customerPin, packagePin, pinCount) {
	    if (!customerPin || customerPin.size === 0 || !packagePin) {
	        return false;
	    }
	    const exposedPadPatterns = ['0', `${pinCount}`, 'ep', 'pad', 'epad'];
	    if (Array.from(packagePin).every((pin) => {
	        return exposedPadPatterns.includes(pin.toLowerCase());
	    }) &&
	        Array.from(customerPin).every((pin) => {
	            return exposedPadPatterns.includes(pin.toLowerCase());
	        })) {
	        return true;
	    }
	    return false;
	}
	
	return compareExposedPad;
}

var DeepConvertMaps = {};

var hasRequiredDeepConvertMaps;

function requireDeepConvertMaps () {
	if (hasRequiredDeepConvertMaps) return DeepConvertMaps;
	hasRequiredDeepConvertMaps = 1;
	Object.defineProperty(DeepConvertMaps, "__esModule", { value: true });
	DeepConvertMaps.deepConvertMaps = deepConvertMaps;
	function deepConvertMaps(value) {
	    if (value instanceof Map) {
	        return deepConvertMaps(Object.fromEntries(value));
	    }
	    if (value instanceof Set) {
	        return Array.from(value).map(deepConvertMaps);
	    }
	    if (Array.isArray(value)) {
	        return value.map(deepConvertMaps);
	    }
	    if (value !== null && typeof value === 'object') {
	        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, deepConvertMaps(v)]));
	    }
	    return value;
	}
	
	return DeepConvertMaps;
}

var hasRequiredUtils$4;

function requireUtils$4 () {
	if (hasRequiredUtils$4) return Utils$5;
	hasRequiredUtils$4 = 1;
	(function (exports) {
		var __createBinding = (Utils$5 && Utils$5.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Utils$5 && Utils$5.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.Transform = void 0;
		__exportStar(requireChainableIterator(), exports);
		__exportStar(requireIterableMap(), exports);
		__exportStar(requireIterableSet(), exports);
		__exportStar(requireStrictMap(), exports);
		__exportStar(requireStrictSet(), exports);
		__exportStar(requireStrictTypeGuard(), exports);
		exports.Transform = requireTransform();
		__exportStar(requireAngles(), exports);
		__exportStar(requireAsciiConversion(), exports);
		__exportStar(requireModelBoundingBoxUtils(), exports);
		__exportStar(requireClassConstructor(), exports);
		__exportStar(requireDateUtils(), exports);
		__exportStar(require_Error(), exports);
		__exportStar(requireHash(), exports);
		__exportStar(requireNamedCallStack(), exports);
		__exportStar(requireNaturalStringSort(), exports);
		__exportStar(requireNormalizeSiString(), exports);
		__exportStar(requireObjectUtils(), exports);
		__exportStar(requireParsedCallStack(), exports);
		__exportStar(requirePromiseAllSettledWithAbort(), exports);
		__exportStar(requirePromiseAllSettled(), exports);
		__exportStar(requireRecordTypeFromMap(), exports);
		__exportStar(requireRequireFields(), exports);
		__exportStar(requireSleep(), exports);
		__exportStar(requireStaticImplements(), exports);
		__exportStar(requireStringUtils(), exports);
		__exportStar(requireSummarizedAggregateError(), exports);
		__exportStar(requireThunkMap(), exports);
		__exportStar(requireToArray(), exports);
		__exportStar(requireTypeUtils(), exports);
		__exportStar(requireUtils$5(), exports);
		__exportStar(requireValueValidation(), exports);
		__exportStar(requireZodOutputType(), exports);
		__exportStar(requireZodPartial(), exports);
		__exportStar(requireZodFieldPresence(), exports);
		__exportStar(requireCompareExposedPad(), exports);
		__exportStar(requireDeepConvertMaps(), exports);
		__exportStar(requireSIValueTool(), exports);
		__exportStar(requireRkmCodeValueTool(), exports);
		
	} (Utils$5));
	return Utils$5;
}

var UtilsExports = requireUtils$4();

var ObjectSelectionType;
(function (ObjectSelectionType) {
    ObjectSelectionType[ObjectSelectionType["None"] = 0] = "None";
    ObjectSelectionType[ObjectSelectionType["Object"] = 1] = "Object";
    ObjectSelectionType[ObjectSelectionType["Net"] = 2] = "Net";
    ObjectSelectionType[ObjectSelectionType["Any"] = 3] = "Any";
})(ObjectSelectionType || (ObjectSelectionType = {}));
var DrawingColorizationMode;
(function (DrawingColorizationMode) {
    DrawingColorizationMode[DrawingColorizationMode["Layer"] = 0] = "Layer";
    DrawingColorizationMode[DrawingColorizationMode["Net"] = 1] = "Net";
    DrawingColorizationMode[DrawingColorizationMode["ObjectAndNet"] = 2] = "ObjectAndNet";
})(DrawingColorizationMode || (DrawingColorizationMode = {}));
class DrawingElement extends EventEmitter {
    manager;
    modelObject;
    modelObjectType;
    selectionType;
    _sealed = false;
    _selected = false;
    _interactiveEnabled = false;
    /**
     * "Cache" in this variable name is misleading. There is an implicit global `selectionState`, but it's not stored
     * anywhere else, so each drawing element has to store it for itself.
     **/
    _selectionStateCache = false;
    _layers = [];
    _container = new PIXI.Container();
    _mappedEventHandlers = new Map([['hoverstatechanged', []]]);
    /** A map of layer IDs to {@link GraphicEntry}s */
    _graphics = new Map();
    /** Maps layer IDs (numbers) to the {@link PIXI.Container}s that contains the layer's drawing elements. */
    _layerContainers = new Map();
    _tags;
    _globalProperties;
    _properties;
    _highlightedProperties;
    _isGlobalHighlighted;
    _graphicsWithoutProperties;
    /**
     * The subset of graphics that matched the last `selectProperties` call. Used for partial selections, i.e., selection
     * of only certain graphics of the element.
     */
    _selectedGraphics;
    _renderOptsChangeListeners;
    _graphicsCount = 0;
    _hoverable;
    _hoverHandler;
    _isHovered = false;
    _colorizationMode;
    /**
     * For the current colourization mode, were we able to find appropriate colour options for the drawing element? Read
     * this as, "this drawing element 'has colour options for [the] mode' <currentColorizationMode>".
     **/
    _hasColorOptsForMode;
    _manualColor;
    _link;
    _name;
    constructor(manager, modelObject, modelObjectType, selectionType, opts) {
        super();
        this.manager = manager;
        this.modelObject = modelObject;
        this.modelObjectType = modelObjectType;
        this.selectionType = selectionType;
        if (opts?.properties) {
            this._globalProperties = new Map(Object.entries(opts.properties)
                .filter(([, values]) => typeof values !== 'undefined')
                .map(([name, values]) => [name, new Set(UtilsExports.toArray(values))]));
        }
        if (opts?.link) {
            this._link = opts.link;
        }
        if (opts?.name) {
            this._name = opts.name;
            this._container.name = opts.name;
        }
        this.setInteractionEnabled(true);
        this.setHoverable(true);
    }
    get interactionEnabled() {
        return this._interactiveEnabled;
    }
    get selected() {
        return this._selected;
    }
    get isVisible() {
        return this._container.visible;
    }
    get isHovered() {
        return this._isHovered && this.interactionEnabled && !this._selected;
    }
    // Single-purpose method for use in `ProbeTool` unfortunately
    get isHoveredIrregardlessOfSelection() {
        return this._isHovered;
    }
    get layerIds() {
        return this._layers;
    }
    get container() {
        return this._container;
    }
    get sealed() {
        return this._sealed;
    }
    get numGraphics() {
        return this._graphicsCount;
    }
    get colorizationMode() {
        return typeof this._colorizationMode !== 'undefined' ? this._colorizationMode : DrawingColorizationMode.Layer;
    }
    get manualColor() {
        return this._manualColor;
    }
    getLayerRenderOptsForGraphic(graphic) {
        return graphic.opts?.layerOpts;
    }
    get objectId() {
        if (this._globalProperties?.has(DrawingProperties.ObjectId)) {
            return this._globalProperties.get(DrawingProperties.ObjectId).keys().next().value;
        }
        if (!this._properties?.has(DrawingProperties.ObjectId)) {
            return undefined;
        }
        return this._properties.get(DrawingProperties.ObjectId).keys().next().value;
    }
    get isLink() {
        return Boolean(this._link);
    }
    get linkDestination() {
        return this._link;
    }
    /**
     * Sets which layers should be visible. `undefined` means all layers should be visible.
     *
     * @param layers - The layers to make visible.
     */
    setVisibleLayers(layers) {
        for (const [layer, container] of this._layerContainers.entries()) {
            // If no layers were specified, make all layers visible. Otherwise, only make this layer container visible if it's
            // in the `layers` list.
            container.visible = !layers || layers.indexOf(layer) !== -1;
        }
        this.maybeLoadColorizationOpts(undefined, typeof layers !== 'undefined');
    }
    setVisibleObjectTypes(types) {
        const allVisible = typeof types === 'undefined';
        for (const graphics of this._graphics.values()) {
            for (const graphic of graphics) {
                graphic.graphic.visible =
                    allVisible || Boolean(graphic.object?.type && types.find((t) => t === graphic.object?.type));
            }
        }
    }
    setColorizationMode(mode, _force) {
        const previousMode = this.colorizationMode;
        switch (mode) {
            // If we're colourizing an entire layer, the individual elements shouldn't be colourized themselves
            case DrawingColorizationMode.Layer:
                if (typeof this._colorizationMode !== 'undefined') {
                    delete this._colorizationMode;
                }
                break;
            default:
                this._colorizationMode = mode;
        }
        // Only do stuff if the colourization mode has changed
        if (previousMode !== mode) {
            if (mode === DrawingColorizationMode.Layer) {
                // In the case of the `Layer` colourization mode, we need to remove the stored colourization options from all
                // graphics.
                this.reapplySelectionVisuals();
                if (this._hasColorOptsForMode) {
                    delete this._hasColorOptsForMode;
                    for (const graphics of this._graphics.values()) {
                        for (const graphic of graphics) {
                            this.removeGraphicColorOpts(graphic);
                        }
                    }
                }
            }
            else {
                // If we had stored colourization options on the `GraphicEntry`s, but for a different mode, we need to remove
                // them.
                if (typeof this._hasColorOptsForMode !== 'undefined' && this._hasColorOptsForMode !== mode) {
                    for (const graphics of this._graphics.values()) {
                        for (const graphic of graphics) {
                            this.removeGraphicColorOpts(graphic);
                        }
                    }
                }
                // This gets the appropriate colourization options onto this drawing element's `GraphicEntry`s.
                this.maybeLoadColorizationOpts(mode);
            }
        }
    }
    /** Remove the stored colourization options from a given `GraphicEntry`. */
    removeGraphicColorOpts(graphic) {
        if (graphic.opts?.colorOpts) {
            if (graphic.opts.layerOpts) {
                delete graphic.opts.colorOpts;
            }
            else {
                delete graphic.opts;
            }
        }
    }
    /**
     * Store the relevant colourization options ({@link GraphicRenderOpts}) onto each {@link GraphicEntry} of this drawing
     * element, but only if they're needed. Skips storing the options if the drawing element already has them.
     *
     * @param mode    - The colourization mode to get the options for.
     * @param recolor - Whether or not to force a recolour, in which case the colourization options will be stored even if
     *                  the {@link GraphicEntry}s already had options stored.
     */
    maybeLoadColorizationOpts(mode = this.colorizationMode, recolor = false) {
        // If the drawing element is not visible, do nothing
        if (this.isVisible) {
            if (
            // If the colourization mode is `Layer`, we don't need to do anything
            mode === DrawingColorizationMode.Layer ||
                // If we already have colour options for the current mode, and we're not forcing a recolour, do nothing
                (mode === this._hasColorOptsForMode && !recolor)) {
                return;
            }
            const selected = Boolean(this._selected);
            for (const graphics of this._graphics.values()) {
                for (const graphic of graphics) {
                    // Do nothing if:
                    // - the graphic is not visible, or
                    // - the graphic has its colour options already, or
                    // - the graphic has no `IDrawingObject` associated with it.
                    if (!graphic.graphic.visible || graphic.opts?.colorOpts || !graphic.object) {
                        continue;
                    }
                    // Get the PIXI.Container associated with the layer that this graphic belongs to
                    const layer = this._layerContainers.get(graphic.layer);
                    if (!layer?.visible) {
                        continue;
                    }
                    const colorOpts = this.manager.netColorProvider?.getColorForNet(
                    // Essentially this means that if the colourization mode is `Layer`, we don't need to colourize the drawing
                    // element.
                    mode === DrawingColorizationMode.ObjectAndNet ? graphic.object : undefined, 
                    // Gets the net name from the `GraphicEntry`'s properties, if it exists
                    UtilsExports.toArray(graphic.properties ? graphic.properties[DrawingProperties.Net] : undefined)[0]);
                    // Set the found colourization options onto the `GraphicEntry`
                    if (graphic.opts) {
                        graphic.opts.colorOpts = colorOpts;
                    }
                    else {
                        graphic.opts = { colorOpts };
                    }
                    // Actually apply the options we just found
                    this.applySelectionState(graphic, selected, this._selectionStateCache);
                }
            }
            this._hasColorOptsForMode = mode;
        }
    }
    *layers() {
        for (const layer of this._layers) {
            yield this.manager.getLayerById(layer);
        }
    }
    *getProperties() {
        if (this._globalProperties) {
            for (const [propName, propValues] of this._globalProperties.entries()) {
                yield { [propName]: Array.from(propValues) };
            }
        }
        if (this._properties) {
            for (const [propName, propValues] of this._properties.entries()) {
                yield { [propName]: Array.from(propValues.keys()) };
            }
        }
    }
    /**
     * Enables or disables pointer interaction for this element. A selected element stays interactive
     * so it can still be pressed and hovered; only its hover visual is suppressed (see `isHovered`).
     *
     * @param enabled - Whether the element and its interactable graphics receive pointer events.
     */
    setInteractionEnabled(enabled) {
        const oldHovered = this.isHovered;
        this._interactiveEnabled = enabled;
        this._container.interactive = enabled;
        for (const [, graphics] of this._graphics) {
            for (const graphic of graphics) {
                if (!graphic.nonInteractible) {
                    graphic.graphic.interactive = enabled;
                }
            }
        }
        if (oldHovered !== this.isHovered) {
            this.emit('hoverstatechanged', this);
        }
    }
    /**
     * Sets whether this drawing element is selected or not.
     *
     * @param selected       - Whether or not this drawing element should be selected.
     * @param selectionState - Whether or not any drawing elements are selected. If `selectionState` is true, but
     *                         `selected` is false, this drawing element gets dimmer (indicated by the 'unselected'
     *                         graphic "state").
     * @param force          - Force the selection state to be updated even if nothing's changed.
     */
    setSelected(selected, selectionState, force = false) {
        if (!selected) {
            this._selectedGraphics = undefined;
        }
        const oldHovered = this.isHovered;
        if (
        // Only apply the selection state if it has changed
        selected !== this._selected ||
            this._selectionStateCache !== selectionState ||
            // Or if `force` is true
            force) {
            this.applySelectionStateToAllGraphics(selected, selectionState);
        }
        this._selected = selected;
        this._selectionStateCache = selectionState;
        this.setInteractionEnabled(this._interactiveEnabled);
        if (oldHovered !== this.isHovered) {
            this.emit('hoverstatechanged', this);
        }
    }
    applySelectionStateToAllGraphics(selected, selectionState) {
        for (const graphics of this._graphics.values()) {
            for (const graphic of graphics) {
                this.applySelectionState(graphic, selected, selectionState);
            }
        }
    }
    /**
     * Re-applies the current selection visuals to every graphic, preserving partial selections.
     *
     * Unlike `applySelectionStateToAllGraphics(Boolean(this._selected), ...)` — which would promote a
     * partial selection to a full one — this keeps only the graphics matched by the last
     * `selectProperties` call in the 'selected' state.
     */
    reapplySelectionVisuals() {
        if (this._selected === 'partial' && this._selectedGraphics) {
            for (const graphics of this._graphics.values()) {
                for (const graphic of graphics) {
                    this.applySelectionState(graphic, this._selectedGraphics.has(graphic), this._selectionStateCache);
                }
            }
        }
        else {
            this.applySelectionStateToAllGraphics(Boolean(this._selected), this._selectionStateCache);
        }
    }
    setHoverable(hoverable) {
        this._hoverable = Boolean(hoverable);
        if (this._hoverable) {
            if (!this._hoverHandler) {
                this._hoverHandler = (e) => this.onHover(e, this);
                this.on('pointerover', this._hoverHandler);
                this.on('pointerout', this._hoverHandler);
            }
        }
        else if (this._hoverHandler) {
            this.removeListener('pointerover', this._hoverHandler);
            this.removeListener('pointerout', this._hoverHandler);
            this._hoverHandler = undefined;
        }
        if (!this._hoverable && this._isHovered) {
            this._isHovered = false;
            for (const graphics of this._graphics.values()) {
                for (const graphic of graphics) {
                    this.applySelectionState(graphic, Boolean(this._selected), this._selectionStateCache);
                }
            }
            this.emit('hoverstatechanged', this);
        }
    }
    setManualColor(opts) {
        this._manualColor = opts;
        if (!opts) {
            delete this._manualColor;
        }
        for (const graphics of this._graphics.values()) {
            for (const graphic of graphics) {
                this.applySelectionState(graphic, Boolean(this._selected), this._selectionStateCache);
            }
        }
    }
    /**
     * Applies the appropriate visual properties to this element's graphics, based on the selection state given.
     *
     * @param graphic        - The graphic to apply the selection state to.
     * @param selected       - Whether or not this drawing element should be selected.
     * @param selectionState - Whether or not any drawing elements are selected. If `selectionState` is true, but
     *                         `selected` is false, this drawing element gets dimmer (indicated by the 'unselected'
     *                         graphic "state").
     */
    applySelectionState(graphic, selected, selectionState) {
        if (selected) {
            // If we're selecting the drawing element, we need to set the graphic's "state" to "selected". Also move the
            // graphic to the special selection group.
            graphic.activeStateOpts = 'selected';
            if (!graphic.previousZOrder) {
                graphic.previousZOrder = graphic.graphic.zOrder;
            }
            graphic.graphic.zOrder = graphic.layerGroup.zIndex;
            graphic.graphic.parentGroup = this.manager.elementSelectionGroup;
        }
        else {
            // If we're deselecting the drawing element, we need to reset the graphic's "state" to its natural state. Also
            // move the graphic back to its original layer group and z-index.
            if ((this._hoverable && this._isHovered) || this._isGlobalHighlighted || graphic.highlighted) {
                graphic.activeStateOpts = 'hover';
            }
            else if (selectionState) {
                graphic.activeStateOpts = 'unselected';
            }
            else {
                graphic.activeStateOpts = 'selectionNeutral';
            }
            graphic.graphic.parentGroup = graphic.layerGroup;
            graphic.graphic.zOrder = graphic.previousZOrder;
            delete graphic.previousZOrder;
        }
        // If updating below, also update `renderOptsChanged`
        let useOpts;
        switch (this.colorizationMode) {
            // If the colourization mode is `Layer`, we need to use the layer's colourization options
            case DrawingColorizationMode.Layer:
                useOpts = this.getLayerRenderOptsForGraphic(graphic);
                break;
            // Otherwise, we need to use the graphic's own colourization options
            case DrawingColorizationMode.Net:
            case DrawingColorizationMode.ObjectAndNet:
                useOpts = graphic.opts?.colorOpts;
                break;
        }
        // Always prioritize any manual colourization options this drawing element may have
        if (this.manualColor) {
            useOpts = this.manualColor;
        }
        if (useOpts) {
            // This is the part that actually does the colour changing!!
            const stateOpts = useOpts[graphic.activeStateOpts];
            graphic.graphic.tint = stateOpts.tint;
            graphic.graphic.alpha = stateOpts.alpha;
        }
    }
    /** Set the graphic colour based on the given {@link GraphicRenderStateOpts} */
    layerRenderOptsChanged(graphics, opts) {
        for (const graphic of graphics) {
            // If updating below, also update `applySelectionState`
            graphic.opts = {
                layerOpts: opts,
                colorOpts: graphic.opts?.colorOpts,
            };
            const layerOpts = this.getLayerRenderOptsForGraphic(graphic);
            if ((layerOpts && !this._colorizationMode) || this._colorizationMode === DrawingColorizationMode.Layer) {
                graphic.graphic.tint = layerOpts[graphic.activeStateOpts].tint;
                graphic.graphic.alpha = layerOpts[graphic.activeStateOpts].alpha;
            }
        }
    }
    highlightProperties(allHighlightArgs) {
        if (allHighlightArgs) {
            this._highlightedProperties = allHighlightArgs;
        }
        if (!allHighlightArgs) {
            if (this._isGlobalHighlighted) {
                delete this._isGlobalHighlighted;
            }
            if (this._highlightedProperties) {
                delete this._highlightedProperties;
            }
        }
        else if (this._globalProperties) {
            if (this._isGlobalHighlighted) {
                delete this._isGlobalHighlighted;
            }
            for (const [property, values] of allHighlightArgs.entries()) {
                if (this._isGlobalHighlighted) {
                    break;
                }
                if (this._globalProperties.has(property)) {
                    const propMap = this._globalProperties.get(property);
                    for (const value of values) {
                        if (propMap.has(value)) {
                            this._isGlobalHighlighted = true;
                            break;
                        }
                    }
                }
            }
        }
        for (const graphics of this._graphics.values()) {
            for (const graphic of graphics) {
                if (graphic.highlighted) {
                    delete graphic.highlighted;
                }
            }
        }
        if (allHighlightArgs && !this._isGlobalHighlighted && this._properties) {
            for (const [property, values] of allHighlightArgs.entries()) {
                if (!this._properties.has(property)) {
                    continue;
                }
                const propValues = this._properties.get(property);
                for (const highlightValue of values) {
                    if (propValues.has(highlightValue)) {
                        for (const graphic of propValues.get(highlightValue)) {
                            graphic.highlighted = true;
                        }
                    }
                }
            }
        }
        this.reapplySelectionVisuals();
    }
    selectProperties(allSelectionArgs) {
        const oldHovered = this.isHovered;
        if (this._globalProperties) {
            for (const [property, values] of allSelectionArgs.entries()) {
                if (this._globalProperties.has(property)) {
                    const propMap = this._globalProperties.get(property);
                    for (const value of values) {
                        if (propMap.has(value)) {
                            this.setSelected(true, true);
                            return;
                        }
                    }
                }
            }
        }
        if (!this._properties) {
            this.setSelected(false, true);
            return;
        }
        // Apply selection states to graphics without props
        if (this._graphicsWithoutProperties) {
            for (const graphic of this._graphicsWithoutProperties) {
                this.applySelectionState(graphic, false, true);
            }
        }
        // Apply selection states to graphics with props.
        //
        // A single graphic can be registered under several properties (e.g. a connected pin carries both
        // `pin` and `net`). Decide membership first — a graphic is selected if it matches ANY of its
        // properties — then paint each graphic exactly once. Painting per-property instead would let a
        // non-matching property (e.g. `net`, when the selection is by `pin`) overwrite the visual of a
        // graphic that already matched, leaving it dimmed even though it is in `selectedGraphics` (the
        // selection state and the rendered state would then disagree).
        const selectedGraphics = new Set();
        const propertyGraphics = new Set();
        for (const [property, propValues] of this._properties.entries()) {
            const propSelected = allSelectionArgs.has(property);
            for (const [value, graphics] of propValues) {
                const selected = propSelected && allSelectionArgs.get(property).has(value);
                for (const graphic of graphics) {
                    propertyGraphics.add(graphic);
                    if (selected) {
                        selectedGraphics.add(graphic);
                    }
                }
            }
        }
        // A graphic is selected when it matches ANY of its registered properties. Full selection means
        // every property-bearing graphic matched; partial means at least one did. Tracking
        // allSelected/someSelected per-(property,value) pair instead would make a multi-property graphic
        // (e.g. a connected pin carrying both Pin and Net) count as not fully selected when only one of
        // its properties is in the active selection — even though the graphic itself is visually selected.
        const someSelected = selectedGraphics.size > 0;
        const allSelected = propertyGraphics.size > 0 && selectedGraphics.size === propertyGraphics.size;
        for (const graphic of propertyGraphics) {
            this.applySelectionState(graphic, selectedGraphics.has(graphic), true);
        }
        if (allSelected && someSelected) {
            this._selected = true;
            this._selectedGraphics = undefined;
            // Non-property graphics (e.g. module body) were dimmed above; undo that now that the
            // element is fully selected.
            this.applySelectionStateToAllGraphics(true, true);
        }
        else if (someSelected) {
            this._selected = 'partial';
            this._selectedGraphics = selectedGraphics;
        }
        else {
            this._selected = false;
            this._selectedGraphics = undefined;
        }
        this.setInteractionEnabled(this._interactiveEnabled);
        this._selectionStateCache = true;
        if (oldHovered !== this.isHovered) {
            this.emit('hoverstatechanged', this);
        }
    }
    /**
     * Returns the bounding rectangle of this element in its container's local coordinate space.
     *
     * For partially-selected elements (e.g. a single pin highlighted within a module), returns the
     * tight bounds of only the matched graphics rather than the full element bounds — enabling
     * `zoomToSelection` to zoom precisely to a pin or pad rather than the whole component.
     * For fully-selected or unselected elements, falls back to `container.getLocalBounds()`.
     *
     * @returns The bounding rectangle in the container's local space.
     */
    getSelectionBounds() {
        if (this._selected === 'partial' && this._selectedGraphics) {
            let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
            for (const entry of this._selectedGraphics) {
                const lb = entry.graphic.getLocalBounds();
                // Transform all 4 corners from the graphic's local space into this element's container
                // space, correctly handling any rotation/mirroring applied to the graphic.
                for (const [cx, cy] of [
                    [lb.x, lb.y],
                    [lb.x + lb.width, lb.y],
                    [lb.x, lb.y + lb.height],
                    [lb.x + lb.width, lb.y + lb.height],
                ]) {
                    const wp = this.container.toLocal({ x: cx, y: cy }, entry.graphic);
                    x1 = Math.min(x1, wp.x);
                    y1 = Math.min(y1, wp.y);
                    x2 = Math.max(x2, wp.x);
                    y2 = Math.max(y2, wp.y);
                }
            }
            if (x1 !== Infinity) {
                return new PIXI.Rectangle(x1, y1, x2 - x1, y2 - y1);
            }
        }
        return this.container.getLocalBounds();
    }
    setTagVisible(tag, visible, _onLayer) {
        if (this._tags?.has(tag)) {
            for (const element of this._tags.get(tag)) {
                element.graphic.visible = visible;
            }
        }
    }
    setTagDefaultVisibility(tag, onLayer) {
        if (this._tags?.has(tag)) {
            for (const element of this._tags.get(tag)) {
                if (typeof onLayer !== 'number' || element.layer === onLayer) {
                    element.graphic.visible = element.tags[tag].defaultVisible;
                }
            }
        }
    }
    setAllTagDefaultVisibility(onLayer) {
        if (this._tags) {
            for (const [tag, tags] of this._tags.entries()) {
                for (const element of tags) {
                    if (typeof onLayer !== 'number' || element.layer === onLayer) {
                        element.graphic.visible = element.tags[tag].defaultVisible;
                    }
                }
            }
        }
    }
    setVisible(visible) {
        this._container.visible = visible;
        this.maybeLoadColorizationOpts();
    }
    addGraphic(layer, graphic, opts, settings) {
        if (this.sealed) {
            throw new Error('DrawingElement is sealed');
        }
        const layerGroup = this.manager.getLayerGroup(layer, true);
        // Create a new `PIXI.Container` for this drawing element on the specified layer, if it doesn't already exist.
        // Each drawing element creates its own container for each layer it has graphics on.
        if (!this._graphics.has(layer)) {
            this._graphics.set(layer, []);
            const container = new PIXI.Container();
            container.name = `Layer ${layer}`;
            container.visible = this.manager.getLayerIsVisible(layer);
            this._layerContainers.set(layer, container);
            this._container.addChild(container);
            container.parentGroup = layerGroup;
            container.zIndex = layer;
        }
        if (typeof graphic.zOrder === 'number') {
            this._layerContainers.get(layer).sortableChildren = true;
        }
        const entry = {
            layer,
            graphic,
            layerGroup,
            activeStateOpts: 'selectionNeutral',
            tags: {},
        };
        const layerOpts = settings?.contrastWithLayer ? this.manager.colorProvider.getContrastingColorForLayer(layer) : opts;
        if (layerOpts) {
            entry.opts = {
                layerOpts,
            };
        }
        if (settings?.object) {
            entry.object = settings.object;
        }
        if (settings?.contrastWithLayer) {
            entry.contrastWithLayer = true;
        }
        if (settings?.nonInteractible) {
            entry.nonInteractible = true;
        }
        if (settings?.tags) {
            if (!this._tags) {
                this._tags = new Map();
            }
            for (const tag of settings.tags) {
                entry.tags[tag.tag] = tag;
                graphic.visible = tag.defaultVisible;
                if (!this._tags.has(tag.tag)) {
                    this._tags.set(tag.tag, []);
                }
                this._tags.get(tag.tag).push(entry);
            }
        }
        if (settings?.link) {
            this._link = settings.link;
        }
        // Register an event listener on `opts.changeWatcher` which, when the colourization options change, calls
        // `this.layerRenderOptsChanged` to update the layer colours with the new options.
        if (layerOpts?.changeWatcher) {
            if (!this._renderOptsChangeListeners) {
                this._renderOptsChangeListeners = new Map();
            }
            if (!this._renderOptsChangeListeners.has(layerOpts)) {
                const entries = [entry];
                const listener = {
                    listener: (opts) => this.layerRenderOptsChanged(entries, opts),
                    entries,
                };
                this._renderOptsChangeListeners.set(layerOpts, listener);
                layerOpts.changeWatcher.on('changed', listener.listener);
            }
            else {
                const listener = this._renderOptsChangeListeners.get(layerOpts);
                listener.entries.push(entry);
            }
        }
        this._graphics.get(layer).push(entry);
        this._layerContainers.get(layer).addChild(graphic);
        graphic.parentGroup = entry.layerGroup;
        graphic.zIndex = layer;
        ++this._graphicsCount;
        if (settings?.properties) {
            entry.properties = settings.properties;
            if (!this._properties) {
                this._properties = new Map();
            }
            for (const [propName, propValue] of Object.entries(settings.properties)) {
                if (!this._properties.has(propName)) {
                    this._properties.set(propName, new Map(UtilsExports.toArray(propValue).map((value) => [value, [entry]])));
                }
                else {
                    const propCollection = this._properties.get(propName);
                    for (const value of UtilsExports.toArray(propValue)) {
                        if (propCollection.has(value)) {
                            propCollection.get(value).push(entry);
                        }
                        else {
                            propCollection.set(value, [entry]);
                        }
                    }
                }
            }
        }
        else {
            if (this._graphicsWithoutProperties) {
                this._graphicsWithoutProperties.push(entry);
            }
            else {
                this._graphicsWithoutProperties = [entry];
            }
        }
    }
    seal() {
        if (!this._sealed) {
            this.setSelected(!!this.selected, false, true);
            this._sealed = true;
        }
    }
    *graphicsOnLayers(layers) {
        for (const layer of layers) {
            if (!this._graphics.has(layer)) {
                continue;
            }
            for (const { graphic } of this._graphics.get(layer)) {
                yield graphic;
            }
        }
    }
    *allGraphics() {
        for (const graphics of this._graphics.values()) {
            for (const { graphic } of graphics) {
                yield graphic;
            }
        }
    }
    *netGraphics() {
        if (this._properties?.has(DrawingProperties.Net)) {
            for (const [net, graphics] of this._properties.get(DrawingProperties.Net).entries()) {
                // TODO: If we call this alot, think about perf
                yield [net, graphics.map((g) => g.graphic)];
            }
        }
    }
    *nets() {
        if (!this._properties?.has(DrawingProperties.Net)) {
            return;
        }
        yield* this._properties.get(DrawingProperties.Net).keys();
    }
    *tags() {
        if (!this._tags) {
            return;
        }
        yield* this._tags.keys();
    }
    onHover(e, element) {
        const newState = e.type === 'pointerenter' || e.type === 'pointerover';
        const stateChanged = newState !== this._isHovered;
        this._isHovered = e.type === 'pointerenter' || e.type === 'pointerover';
        for (const graphics of this._graphics.values()) {
            for (const graphic of graphics) {
                this.applySelectionState(graphic, Boolean(this._selected), this._selectionStateCache);
            }
        }
        if (stateChanged) {
            this.emit('hoverstatechanged', element);
        }
    }
    on(eventName, listener) {
        if (!this._mappedEventHandlers.has(eventName)) {
            this._mappedEventHandlers.set(eventName, [listener]);
            this._container.on(eventName, (e) => {
                this.emit(eventName, e, this);
            });
        }
        else {
            this._mappedEventHandlers.get(eventName)?.push(listener);
        }
        super.on(eventName, listener);
        return this;
    }
    removeAllListeners(event) {
        if (event) {
            this._mappedEventHandlers.delete(event);
        }
        else {
            this._mappedEventHandlers.clear();
            this._mappedEventHandlers.set('hoverstatechanged', []);
        }
        this._container.removeAllListeners(event);
        super.removeAllListeners(event);
        return this;
    }
    destroy() {
        if (this._renderOptsChangeListeners) {
            for (const [opts, entry] of this._renderOptsChangeListeners.entries()) {
                opts.changeWatcher.removeListener('changed', entry.listener);
            }
            this._renderOptsChangeListeners.clear();
        }
        this._container.destroy({
            children: true,
        });
    }
    removeListener(eventName, listener) {
        if (!this._mappedEventHandlers.has(eventName)) {
            console.warn(`No handlers for event: ${eventName}`);
            return this;
        }
        const map = this._mappedEventHandlers.get(eventName);
        const index = map.indexOf(listener);
        if (index === -1) {
            throw new Error(`Did not have listener for event: ${eventName}`);
        }
        map.splice(index, 1);
        if (map.length === 0 && eventName !== 'hoverstatechanged') {
            this._mappedEventHandlers.delete(eventName);
            this._container.removeAllListeners(eventName);
        }
        super.removeListener(eventName, listener);
        return this;
    }
    once(_eventName, _listener) {
        throw new Error(`MethodNotImplemented`);
    }
    off(_eventName, _listener) {
        throw new Error(`MethodNotImplemented`);
    }
    prependOnceListener(_eventName, _listener) {
        throw new Error(`MethodNotImplemented`);
    }
    prependListener(_eventName, _listener) {
        throw new Error(`MethodNotImplemented`);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    rawListeners(_eventName) {
        throw new Error(`MethodNotImplemented`);
    }
    addListener(_eventName, _listener) {
        throw new Error(`MethodNotImplemented`);
    }
}

var SelectionMode;
(function (SelectionMode) {
    SelectionMode[SelectionMode["None"] = 0] = "None";
    SelectionMode[SelectionMode["SingleObject"] = 1] = "SingleObject";
    SelectionMode[SelectionMode["MultipleObject"] = 2] = "MultipleObject";
    SelectionMode[SelectionMode["SingleNet"] = 3] = "SingleNet";
    SelectionMode[SelectionMode["Rectangular"] = 4] = "Rectangular";
    SelectionMode[SelectionMode["AnySingleNetOrObject"] = 5] = "AnySingleNetOrObject";
})(SelectionMode || (SelectionMode = {}));
var LinkHandlingMode;
(function (LinkHandlingMode) {
    LinkHandlingMode[LinkHandlingMode["Disabled"] = 0] = "Disabled";
    LinkHandlingMode[LinkHandlingMode["SingleClick"] = 1] = "SingleClick";
    LinkHandlingMode[LinkHandlingMode["DoubleClick"] = 2] = "DoubleClick";
})(LinkHandlingMode || (LinkHandlingMode = {}));

var MouseEventButtons;
(function (MouseEventButtons) {
    MouseEventButtons[MouseEventButtons["None"] = -1] = "None";
    MouseEventButtons[MouseEventButtons["Left"] = 0] = "Left";
    MouseEventButtons[MouseEventButtons["Middle"] = 1] = "Middle";
    MouseEventButtons[MouseEventButtons["Right"] = 2] = "Right";
})(MouseEventButtons || (MouseEventButtons = {}));

function getMouseButtonForEvent(event) {
    if (event.data.pointerType === 'touch') {
        return MouseEventButtons.Left;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (event.data.originalEvent.ctrlKey && event.data.button === MouseEventButtons.Left) {
        return MouseEventButtons.Right;
    }
    return event.data.button;
}
function getNumTouchesForEvent(event) {
    const originalEvent = event.data.originalEvent;
    const num = typeof originalEvent.touches?.length === 'number' ?
        originalEvent.touches.length
        : originalEvent.changedTouches?.length;
    return typeof num === 'number' ? num : 1;
}

var Constants$2 = {};

var hasRequiredConstants$5;

function requireConstants$5 () {
	if (hasRequiredConstants$5) return Constants$2;
	hasRequiredConstants$5 = 1;
	Object.defineProperty(Constants$2, "__esModule", { value: true });
	Constants$2.OVERLAY_ANNOTATIONS_COLOR = Constants$2.BackgroundLayer = Constants$2.UserSelectionLayer = Constants$2.CommentLayer = Constants$2.OverlayLayer = Constants$2.SelectedElementsLayer = Constants$2.DEFAULT_TEXTURIZE_POLYGON_THRESHOLD = Constants$2.DEFAULT_SKIP_LARGE_POLYGONS_THRESHOLD = Constants$2.ARC_HITBOX_POLYGON_VERTEX_DEGREES = Constants$2.MIN_CIRCLE_RADIUS = Constants$2.MIN_TEXT_THICKNESS = Constants$2.MIN_TEXT_SIZE = Constants$2.MIN_ROUND_RECT_RADIUS = Constants$2.JUNCTION_RADIUS_DEFAULT = Constants$2.PIN_WIDTH_DEFAULT = Constants$2.SEGMENT_MIN_HITBOX_WIDTH = Constants$2.SEGMENT_MIN_WIDTH = Constants$2.TAG_BBOX = Constants$2.TAG_PAD_LABELS = Constants$2.TAG_TEXT_BOUNDS = Constants$2.TAG_POLYGON_FILL = Constants$2.TAG_OVERLAY = Constants$2.TEXTURIZED_ELEMENT_SCALE = Constants$2.WORLD_SCALE = Constants$2.COLOR_UNFILLED = void 0;
	Constants$2.COLOR_UNFILLED = 0xffffff;
	Constants$2.WORLD_SCALE = 2;
	Constants$2.TEXTURIZED_ELEMENT_SCALE = 100;
	Constants$2.TAG_OVERLAY = 'overlay';
	Constants$2.TAG_POLYGON_FILL = 'polyfill';
	Constants$2.TAG_TEXT_BOUNDS = 'text-bounds';
	Constants$2.TAG_PAD_LABELS = 'pad-labels';
	Constants$2.TAG_BBOX = 'bbox';
	Constants$2.SEGMENT_MIN_WIDTH = 0.1;
	Constants$2.SEGMENT_MIN_HITBOX_WIDTH = 1;
	Constants$2.PIN_WIDTH_DEFAULT = 0.1;
	Constants$2.JUNCTION_RADIUS_DEFAULT = 0.5;
	Constants$2.MIN_ROUND_RECT_RADIUS = 0;
	Constants$2.MIN_TEXT_SIZE = 0.1;
	Constants$2.MIN_TEXT_THICKNESS = 1;
	Constants$2.MIN_CIRCLE_RADIUS = 0.1 / 2;
	Constants$2.ARC_HITBOX_POLYGON_VERTEX_DEGREES = 5;
	Constants$2.DEFAULT_SKIP_LARGE_POLYGONS_THRESHOLD = 10000;
	Constants$2.DEFAULT_TEXTURIZE_POLYGON_THRESHOLD = Number.POSITIVE_INFINITY;
	Constants$2.SelectedElementsLayer = 0xfffd;
	Constants$2.OverlayLayer = 0xfffd;
	Constants$2.CommentLayer = 0xfffe;
	Constants$2.UserSelectionLayer = 0xffff;
	Constants$2.BackgroundLayer = 0;
	Constants$2.OVERLAY_ANNOTATIONS_COLOR = {
	    selected: {
	        tint: 0xffffff,
	        alpha: 1,
	    },
	    hover: {
	        tint: 0xffffff,
	        alpha: 1,
	    },
	    unselected: {
	        tint: 0xffffff,
	        alpha: 1,
	    },
	    selectionNeutral: {
	        tint: 0xffffff,
	        alpha: 1,
	    },
	};
	
	return Constants$2;
}

var ConstantsExports = requireConstants$5();

/**
 * A visual rectangle for drag-selecting drawing elements.
 *
 * As far as I'm aware, drag-selecting is not currently implemented.
 **/
class SelectionRectangle {
    drawingManager;
    renderOpts;
    static DrawingElementId = 'SelectionRect';
    start;
    end;
    container;
    _lineWidth = ConstantsExports.SEGMENT_MIN_WIDTH;
    id = SelectionRectangle.DrawingElementId;
    get lineWidth() {
        return this._lineWidth;
    }
    set lineWidth(value) {
        if (this._lineWidth !== value) {
            this._lineWidth = value;
            this.render();
        }
    }
    get drawingElement() {
        return this.container;
    }
    get enabled() {
        return this.drawingManager.selectionMode === SelectionMode.Rectangular;
    }
    get hasSelection() {
        return Boolean(this.start) && Boolean(this.end);
    }
    constructor(drawingManager, renderOpts) {
        this.drawingManager = drawingManager;
        this.renderOpts = renderOpts;
        this.container = new PIXI.Container();
        this.drawingManager.on('selectionModeChanged', this.handleSelectionModeChanged);
    }
    assertHasSelection() {
        if (!this.hasSelection) {
            throw new Error('Missing selection');
        }
    }
    screenCoords() {
        this.assertHasSelection();
        return {
            x1: this.start.screen.x,
            y1: this.start.screen.y,
            x2: this.end.screen.x,
            y2: this.end.screen.y,
        };
    }
    drawingCoords() {
        this.assertHasSelection();
        return {
            x1: this.start.world.x / ConstantsExports.WORLD_SCALE,
            y1: -this.start.world.y / ConstantsExports.WORLD_SCALE,
            x2: this.end.world.x / ConstantsExports.WORLD_SCALE,
            y2: -this.end.world.y / ConstantsExports.WORLD_SCALE,
        };
    }
    rendererCoords() {
        this.assertHasSelection();
        return {
            x1: this.start.global.x,
            y1: this.start.global.y,
            x2: this.end.global.x,
            y2: this.end.global.y,
        };
    }
    setStart(event) {
        this.start = event;
        this.container.removeChildren();
        this.render();
        this.drawingManager.disableViewportDragDuringCurrentDrag();
    }
    setEnd(event) {
        this.end = event;
        this.container.removeChildren();
        this.render();
    }
    onPointerDown(parsedEvent, event) {
        if (getNumTouchesForEvent(event) > 1 || parsedEvent.button !== MouseEventButtons.Left) {
            return;
        }
        if (this.end) {
            delete this.end;
            this.setStart(parsedEvent);
        }
        else if (this.start) {
            this.setEnd(parsedEvent);
        }
        else {
            this.setStart(parsedEvent);
        }
    }
    onMouseMove(event, e) {
        if (!this.drawingManager.isDragging || getNumTouchesForEvent(e) > 1) {
            return;
        }
        if (!this.start) {
            this.setStart(event);
        }
        else {
            this.setEnd(event);
        }
    }
    onDragStart(parsedEvent, event) {
        if (getNumTouchesForEvent(event) > 1) {
            return;
        }
        this.setStart(parsedEvent);
    }
    onDragEnd(parsedEvent, event) {
        if (getNumTouchesForEvent(event) > 1) {
            return;
        }
        this.setEnd(parsedEvent);
    }
    clearSelection() {
        if (this.start) {
            delete this.start;
        }
        if (this.end) {
            delete this.end;
        }
    }
    render() {
        if (!this.start) {
            return;
        }
        this.container.x = this.start.world.x;
        this.container.y = this.start.world.y;
        const g = new PIXI.Graphics();
        g.beginFill(ConstantsExports.COLOR_UNFILLED, this.renderOpts.unselected.alpha);
        g.moveTo(0, 0);
        if (this.end) {
            const lineWidth = ConstantsExports.WORLD_SCALE * this.lineWidth;
            const width = this.end.world.x - this.container.x;
            const height = this.end.world.y - this.container.y;
            g.lineStyle(lineWidth, ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
            g.lineTo(width, 0);
            g.lineTo(width, height);
            g.lineTo(0, height);
            g.lineTo(0, height > 0 ? -lineWidth / 2 : lineWidth / 2);
            g.endFill();
        }
        else {
            const radius = this.drawingManager.viewport.toWorld(10, 0).x - this.drawingManager.viewport.toWorld(0, 0).x;
            g.beginFill(ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
            g.drawCircle(0, 0, radius);
            g.endFill();
        }
        g.tint = this.renderOpts.selected.tint;
        this.container.addChild(g);
    }
    handleSelectionModeChanged = (selectionMode) => {
        if (selectionMode !== SelectionMode.Rectangular) {
            this.clearSelection();
            this.container.removeChildren();
        }
        if (selectionMode === SelectionMode.Rectangular) {
            this.drawingManager.renderer.setDragOpts({
                mouseButtons: 'middle',
            });
        }
        else {
            this.drawingManager.renderer.setDragOpts({
                mouseButtons: 'middle-right',
            });
        }
    };
    destroy() {
        this.clearSelection();
        if (!this.container.destroyed) {
            this.container.destroy();
        }
        this.drawingManager.removeListener('selectionModeChanged', this.handleSelectionModeChanged);
    }
}

var dist$2 = {};

var AgentChat = {};

var chatSurfaceAgents = {};

var hasRequiredChatSurfaceAgents;

function requireChatSurfaceAgents () {
	if (hasRequiredChatSurfaceAgents) return chatSurfaceAgents;
	hasRequiredChatSurfaceAgents = 1;
	Object.defineProperty(chatSurfaceAgents, "__esModule", { value: true });
	chatSurfaceAgents.CHAT_SURFACE_AGENT_IDS = void 0;
	chatSurfaceAgents.CHAT_SURFACE_AGENT_IDS = {
	    results: 'cadstrom-results-assistant',
	    wizard: 'cadstrom-wizard-assistant',
	    packageStore: 'cadstrom-package-store-assistant',
	    datasheet: 'cadstrom-datasheet-assistant',
	    default: 'cadstrom-project-assistant',
	};
	
	return chatSurfaceAgents;
}

var filterSchemas$2 = {};

var QueryBuilder = {};

var types$6 = {};

var hasRequiredTypes$6;

function requireTypes$6 () {
	if (hasRequiredTypes$6) return types$6;
	hasRequiredTypes$6 = 1;
	Object.defineProperty(types$6, "__esModule", { value: true });
	types$6.DEFAULT_RESERVED_COLUMNS = types$6.VALID_FIELD_NAME_PATTERN = types$6.LOGIC_OPERATORS = types$6.COLUMN_FILTER_OPERATORS = types$6.SortDirection = types$6.FieldType = void 0;
	var FieldType;
	(function (FieldType) {
	    FieldType["String"] = "string";
	    FieldType["Boolean"] = "boolean";
	    FieldType["Number"] = "number";
	    FieldType["Date"] = "date";
	    FieldType["Relation"] = "relation";
	    FieldType["Custom"] = "custom";
	})(FieldType || (types$6.FieldType = FieldType = {}));
	var SortDirection;
	(function (SortDirection) {
	    SortDirection["Asc"] = "asc";
	    SortDirection["Desc"] = "desc";
	})(SortDirection || (types$6.SortDirection = SortDirection = {}));
	types$6.COLUMN_FILTER_OPERATORS = [
	    'contains',
	    'doesNotContain',
	    'equals',
	    'doesNotEqual',
	    'startsWith',
	    'endsWith',
	    'isEmpty',
	    'isNotEmpty',
	    'is',
	    'not',
	    'isAnyOf',
	    'isNoneOf',
	    'greaterThan',
	    'greaterThanOrEqual',
	    'lessThan',
	    'lessThanOrEqual',
	];
	types$6.LOGIC_OPERATORS = {
	    AND: 'and',
	    OR: 'or',
	};
	types$6.VALID_FIELD_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
	types$6.DEFAULT_RESERVED_COLUMNS = ['projectId', 'buildId', 'ownerId', 'userId', 'organizationId'];
	
	return types$6;
}

var filterSchema = {};

var hasRequiredFilterSchema;

function requireFilterSchema () {
	if (hasRequiredFilterSchema) return filterSchema;
	hasRequiredFilterSchema = 1;
	Object.defineProperty(filterSchema, "__esModule", { value: true });
	filterSchema.createFilterSchema = createFilterSchema;
	const types_1 = requireTypes$6();
	function createFilterSchema(schema, options) {
	    const reservedColumns = new Set(options?.reservedColumns ?? types_1.DEFAULT_RESERVED_COLUMNS);
	    for (const [fieldName, fieldDef] of Object.entries(schema.fields)) {
	        if (!types_1.VALID_FIELD_NAME_PATTERN.test(fieldName)) {
	            throw new Error(`Invalid field name "${fieldName}" in filter schema. ` +
	                `Field names must match ${types_1.VALID_FIELD_NAME_PATTERN.toString()}.`);
	        }
	        const resolvedColumns = getResolvedColumns(fieldName, fieldDef);
	        for (const col of resolvedColumns) {
	            if (reservedColumns.has(col)) {
	                throw new Error(`Field "${fieldName}" maps to reserved column "${col}". ` +
	                    `Tenancy columns must not be exposed as filterable fields.`);
	            }
	            if (!types_1.VALID_FIELD_NAME_PATTERN.test(col)) {
	                throw new Error(`Field "${fieldName}" references invalid column name "${col}". ` +
	                    `Column names must match ${types_1.VALID_FIELD_NAME_PATTERN.toString()}.`);
	            }
	        }
	    }
	    return { ...schema, __validated: true };
	}
	function getResolvedColumns(fieldName, fieldDef) {
	    const { options } = fieldDef;
	    switch (options.type) {
	        case types_1.FieldType.String:
	        case types_1.FieldType.Boolean:
	        case types_1.FieldType.Number:
	        case types_1.FieldType.Date: {
	            return [options.column ?? fieldName];
	        }
	        case types_1.FieldType.Relation: {
	            return [options.relation, ...options.searchColumns];
	        }
	        case types_1.FieldType.Custom: {
	            return [];
	        }
	    }
	}
	
	return filterSchema;
}

var tenantScopedQueryBuilder = {};

var operatorHandlers = {};

var hasRequiredOperatorHandlers;

function requireOperatorHandlers () {
	if (hasRequiredOperatorHandlers) return operatorHandlers;
	hasRequiredOperatorHandlers = 1;
	Object.defineProperty(operatorHandlers, "__esModule", { value: true });
	operatorHandlers.parseIsAnyOfValues = parseIsAnyOfValues;
	operatorHandlers.buildStringOperatorClause = buildStringOperatorClause;
	operatorHandlers.parseNumberValue = parseNumberValue;
	operatorHandlers.buildNumberOperatorClause = buildNumberOperatorClause;
	operatorHandlers.parseDateValue = parseDateValue;
	operatorHandlers.buildDateOperatorClause = buildDateOperatorClause;
	operatorHandlers.buildBooleanOperatorClause = buildBooleanOperatorClause;
	operatorHandlers.buildRelationOperatorClause = buildRelationOperatorClause;
	function parseIsAnyOfValues(value) {
	    if (value === undefined || value === '') {
	        throw new Error('isAnyOf filter requires a value.');
	    }
	    try {
	        const parsed = JSON.parse(value);
	        if (Array.isArray(parsed)) {
	            const values = parsed
	                .filter((item) => {
	                const t = typeof item;
	                return t === 'string' || t === 'number' || t === 'boolean';
	            })
	                .map(String);
	            if (values.length === 0) {
	                throw new Error('isAnyOf filter contains no valid string values.');
	            }
	            return values;
	        }
	        else if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
	            return [String(parsed)];
	        }
	        throw new Error(`isAnyOf filter received unsupported JSON type: ${typeof parsed}.`);
	    }
	    catch (e) {
	        if (e instanceof SyntaxError) {
	            const values = value
	                .split(',')
	                .map((v) => v.trim())
	                .filter(Boolean);
	            if (values.length === 0) {
	                throw new Error('isAnyOf filter value produced no valid entries.');
	            }
	            return values;
	        }
	        throw e;
	    }
	}
	function buildStringOperatorClause(column, operator, value, options) {
	    switch (operator) {
	        case 'contains':
	            return { [column]: { contains: value, mode: 'insensitive' } };
	        case 'doesNotContain':
	            return { NOT: { [column]: { contains: value, mode: 'insensitive' } } };
	        case 'equals':
	            return { [column]: { equals: value } };
	        case 'doesNotEqual':
	            return { [column]: { not: { equals: value } } };
	        case 'startsWith':
	            return { [column]: { startsWith: value, mode: 'insensitive' } };
	        case 'endsWith':
	            return { [column]: { endsWith: value, mode: 'insensitive' } };
	        case 'isEmpty':
	            if (options?.nonNullable) {
	                return { [column]: { equals: '' } };
	            }
	            return { [column]: { in: [null, ''] } };
	        case 'isNotEmpty':
	            if (options?.nonNullable) {
	                return { [column]: { not: { equals: '' } } };
	            }
	            return { [column]: { not: { in: [null, ''] } } };
	        case 'is':
	            return { [column]: { equals: value } };
	        case 'not':
	            return { [column]: { not: { equals: value } } };
	        case 'isAnyOf': {
	            const values = parseIsAnyOfValues(value);
	            return { [column]: { in: values } };
	        }
	        case 'isNoneOf': {
	            const values = parseIsAnyOfValues(value);
	            return { [column]: { not: { in: values } } };
	        }
	        default:
	            return null;
	    }
	}
	function parseNumberValue(value) {
	    if (value === undefined || value === '') {
	        throw new Error('Number filter requires a value.');
	    }
	    const n = Number(value);
	    if (!Number.isFinite(n)) {
	        throw new Error(`Number filter received non-numeric value: ${value}`);
	    }
	    return n;
	}
	function parseNumberIsAnyOfValues(value) {
	    const raw = parseIsAnyOfValues(value);
	    return raw.map(parseNumberValue);
	}
	function buildNumberOperatorClause(column, operator, value) {
	    switch (operator) {
	        case 'equals':
	        case 'is':
	            return { [column]: { equals: parseNumberValue(value) } };
	        case 'doesNotEqual':
	        case 'not':
	            return { [column]: { not: { equals: parseNumberValue(value) } } };
	        case 'greaterThan':
	            return { [column]: { gt: parseNumberValue(value) } };
	        case 'greaterThanOrEqual':
	            return { [column]: { gte: parseNumberValue(value) } };
	        case 'lessThan':
	            return { [column]: { lt: parseNumberValue(value) } };
	        case 'lessThanOrEqual':
	            return { [column]: { lte: parseNumberValue(value) } };
	        case 'isEmpty':
	            return { [column]: null };
	        case 'isNotEmpty':
	            return { [column]: { not: null } };
	        case 'isAnyOf':
	            return { [column]: { in: parseNumberIsAnyOfValues(value) } };
	        case 'isNoneOf':
	            return { [column]: { not: { in: parseNumberIsAnyOfValues(value) } } };
	        default:
	            return null;
	    }
	}
	function parseDateValue(value) {
	    if (value === undefined || value === '') {
	        throw new Error('Date filter requires a value.');
	    }
	    const trimmed = value.trim();
	    const parsed = /^-?\d+$/.test(trimmed) ? new Date(Number(trimmed)) : new Date(trimmed);
	    if (Number.isNaN(parsed.getTime())) {
	        throw new Error(`Date filter received an unparseable value: ${value}`);
	    }
	    return parsed;
	}
	function parseDateIsAnyOfValues(value) {
	    return parseIsAnyOfValues(value).map(parseDateValue);
	}
	function buildDateOperatorClause(column, operator, value) {
	    switch (operator) {
	        case 'equals':
	        case 'is':
	            return { [column]: { equals: parseDateValue(value) } };
	        case 'doesNotEqual':
	        case 'not':
	            return { [column]: { not: { equals: parseDateValue(value) } } };
	        case 'greaterThan':
	            return { [column]: { gt: parseDateValue(value) } };
	        case 'greaterThanOrEqual':
	            return { [column]: { gte: parseDateValue(value) } };
	        case 'lessThan':
	            return { [column]: { lt: parseDateValue(value) } };
	        case 'lessThanOrEqual':
	            return { [column]: { lte: parseDateValue(value) } };
	        case 'isEmpty':
	            return { [column]: null };
	        case 'isNotEmpty':
	            return { [column]: { not: null } };
	        case 'isAnyOf':
	            return { [column]: { in: parseDateIsAnyOfValues(value) } };
	        case 'isNoneOf':
	            return { [column]: { not: { in: parseDateIsAnyOfValues(value) } } };
	        default:
	            return null;
	    }
	}
	function buildBooleanOperatorClause(column, operator, value) {
	    switch (operator) {
	        case 'is':
	        case 'equals':
	            return { [column]: value === 'true' };
	        case 'not':
	        case 'doesNotEqual':
	            return { [column]: value !== 'true' };
	        default:
	            return null;
	    }
	}
	function buildRelationOperatorClause(relation, searchColumns, operator, value) {
	    const buildColumnOR = (prismaOp, opValue, extraProps) => {
	        return searchColumns.map((col) => ({
	            [col]: { [prismaOp]: opValue, ...extraProps },
	        }));
	    };
	    switch (operator) {
	        case 'contains':
	            return { [relation]: { some: { OR: buildColumnOR('contains', value, { mode: 'insensitive' }) } } };
	        case 'doesNotContain':
	            return { [relation]: { none: { OR: buildColumnOR('contains', value, { mode: 'insensitive' }) } } };
	        case 'equals':
	        case 'is':
	            return { [relation]: { some: { OR: buildColumnOR('equals', value) } } };
	        case 'doesNotEqual':
	        case 'not':
	            return { [relation]: { none: { OR: buildColumnOR('equals', value) } } };
	        case 'startsWith':
	            return { [relation]: { some: { OR: buildColumnOR('startsWith', value, { mode: 'insensitive' }) } } };
	        case 'endsWith':
	            return { [relation]: { some: { OR: buildColumnOR('endsWith', value, { mode: 'insensitive' }) } } };
	        case 'isEmpty':
	            return { [relation]: { none: {} } };
	        case 'isNotEmpty':
	            return { [relation]: { some: {} } };
	        case 'isAnyOf': {
	            const values = parseIsAnyOfValues(value);
	            return { [relation]: { some: { OR: buildColumnOR('in', values) } } };
	        }
	        case 'isNoneOf': {
	            const values = parseIsAnyOfValues(value);
	            return { [relation]: { none: { OR: buildColumnOR('in', values) } } };
	        }
	        default:
	            return null;
	    }
	}
	
	return operatorHandlers;
}

var hasRequiredTenantScopedQueryBuilder;

function requireTenantScopedQueryBuilder () {
	if (hasRequiredTenantScopedQueryBuilder) return tenantScopedQueryBuilder;
	hasRequiredTenantScopedQueryBuilder = 1;
	Object.defineProperty(tenantScopedQueryBuilder, "__esModule", { value: true });
	tenantScopedQueryBuilder.flatFiltersToTree = flatFiltersToTree;
	tenantScopedQueryBuilder.buildTenantScopedWhere = buildTenantScopedWhere;
	tenantScopedQueryBuilder.buildTenantScopedOrderBy = buildTenantScopedOrderBy;
	const operator_handlers_1 = requireOperatorHandlers();
	const types_1 = requireTypes$6();
	function flatFiltersToTree(filters, logicOperator = 'and') {
	    const conditions = filters.map((f) => ({
	        type: 'condition',
	        field: f.field,
	        operator: f.operator,
	        value: f.value,
	    }));
	    return { type: logicOperator === 'or' ? 'or' : 'and', filters: conditions };
	}
	function validateCondition(schema, condition, ignoredSet, rejected) {
	    if (ignoredSet.has(condition.field)) {
	        return null;
	    }
	    if (!types_1.VALID_FIELD_NAME_PATTERN.test(condition.field)) {
	        rejected.push(condition.field);
	        return null;
	    }
	    if (!Object.hasOwn(schema.fields, condition.field)) {
	        rejected.push(condition.field);
	        return null;
	    }
	    const fieldDef = schema.fields[condition.field];
	    if (fieldDef.allowedOperators && !fieldDef.allowedOperators.includes(condition.operator)) {
	        rejected.push(`${condition.field}:${condition.operator}`);
	        return null;
	    }
	    return fieldDef;
	}
	function buildFieldClause(fieldDef, condition) {
	    try {
	        return buildFieldClauseUnsafe(fieldDef, condition);
	    }
	    catch {
	        return null;
	    }
	}
	function buildFieldClauseUnsafe(fieldDef, condition) {
	    const { options } = fieldDef;
	    switch (options.type) {
	        case types_1.FieldType.String: {
	            const column = options.column ?? condition.field;
	            return (0, operator_handlers_1.buildStringOperatorClause)(column, condition.operator, condition.value, {
	                nonNullable: options.nonNullable,
	            });
	        }
	        case types_1.FieldType.Boolean: {
	            const column = options.column ?? condition.field;
	            return (0, operator_handlers_1.buildBooleanOperatorClause)(column, condition.operator, condition.value);
	        }
	        case types_1.FieldType.Number: {
	            const column = options.column ?? condition.field;
	            return (0, operator_handlers_1.buildNumberOperatorClause)(column, condition.operator, condition.value);
	        }
	        case types_1.FieldType.Date: {
	            const column = options.column ?? condition.field;
	            return (0, operator_handlers_1.buildDateOperatorClause)(column, condition.operator, condition.value);
	        }
	        case types_1.FieldType.Relation: {
	            return (0, operator_handlers_1.buildRelationOperatorClause)(options.relation, options.searchColumns, condition.operator, condition.value);
	        }
	        case types_1.FieldType.Custom: {
	            return options.buildWhere(condition.operator, condition.value);
	        }
	    }
	}
	function processFilterNode(node, schema, ignoredSet, rejected) {
	    if (node.type === 'condition') {
	        const fieldDef = validateCondition(schema, node, ignoredSet, rejected);
	        if (!fieldDef) {
	            return null;
	        }
	        return buildFieldClause(fieldDef, node);
	    }
	    const childClauses = node.filters
	        .map((child) => processFilterNode(child, schema, ignoredSet, rejected))
	        .filter((clause) => clause !== null && Object.keys(clause).length > 0);
	    if (childClauses.length === 0) {
	        return null;
	    }
	    if (childClauses.length === 1) {
	        return childClauses[0];
	    }
	    if (node.type === 'or') {
	        return { OR: childClauses };
	    }
	    return { AND: childClauses };
	}
	function buildTenantScopedWhere(tenancyClause, schema, userFilters, additionalFilters) {
	    const rejected = [];
	    const ignoredSet = new Set(schema.ignoredFields ?? []);
	    const userClause = userFilters ? processFilterNode(userFilters, schema, ignoredSet, rejected) : null;
	    const andConditions = [];
	    if (Object.keys(tenancyClause).length > 0) {
	        andConditions.push(tenancyClause);
	    }
	    if (additionalFilters) {
	        for (const filter of additionalFilters) {
	            if (Object.keys(filter).length > 0) {
	                andConditions.push(filter);
	            }
	        }
	    }
	    if (userClause && Object.keys(userClause).length > 0) {
	        andConditions.push(userClause);
	    }
	    if (andConditions.length === 0) {
	        return { where: {}, rejected };
	    }
	    return { where: { AND: andConditions }, rejected };
	}
	function buildFieldOrderBy(fieldDef, sortOption) {
	    const { options } = fieldDef;
	    const direction = sortOption.direction;
	    switch (options.type) {
	        case types_1.FieldType.Relation: {
	            return [];
	        }
	        case types_1.FieldType.Custom: {
	            if (options.buildOrderBy) {
	                return options.buildOrderBy(direction);
	            }
	            return [];
	        }
	        case types_1.FieldType.String:
	        case types_1.FieldType.Boolean:
	        case types_1.FieldType.Number:
	        case types_1.FieldType.Date: {
	            const column = options.column ?? sortOption.field;
	            return [{ [column]: direction }];
	        }
	    }
	}
	function buildTenantScopedOrderBy(schema, sort, defaultOrderBy) {
	    const fallback = defaultOrderBy ?? [{ createdAt: 'desc' }];
	    if (!sort || sort.length === 0) {
	        return fallback;
	    }
	    const orderByItems = [];
	    for (const sortOption of sort) {
	        if (!types_1.VALID_FIELD_NAME_PATTERN.test(sortOption.field)) {
	            continue;
	        }
	        if (!Object.hasOwn(schema.fields, sortOption.field)) {
	            continue;
	        }
	        const fieldDef = schema.fields[sortOption.field];
	        const items = buildFieldOrderBy(fieldDef, sortOption);
	        orderByItems.push(...items);
	    }
	    return orderByItems.length > 0 ? orderByItems : fallback;
	}
	
	return tenantScopedQueryBuilder;
}

var filterSchemaTestUtils = {};

var hasRequiredFilterSchemaTestUtils;

function requireFilterSchemaTestUtils () {
	if (hasRequiredFilterSchemaTestUtils) return filterSchemaTestUtils;
	hasRequiredFilterSchemaTestUtils = 1;
	Object.defineProperty(filterSchemaTestUtils, "__esModule", { value: true });
	filterSchemaTestUtils.assertFilterSchemaSecurity = assertFilterSchemaSecurity;
	const tenant_scoped_query_builder_1 = requireTenantScopedQueryBuilder();
	const types_1 = requireTypes$6();
	const ATTACK_FIELD_NAMES = [
	    '__proto__',
	    'constructor',
	    'toString',
	    'prototype',
	    'project.organizationId',
	    'build.id',
	    'org.id',
	];
	function assertFilterSchemaSecurity(schema, options) {
	    const tenancy = options?.tenancyClause ?? { buildId: 'test-build-id' };
	    const reserved = options?.reservedColumns ?? types_1.DEFAULT_RESERVED_COLUMNS;
	    if (!schema.__validated) {
	        throw new Error('Schema was not created via createFilterSchema(). ' +
	            'Construct schemas with createFilterSchema() to ensure startup validation runs.');
	    }
	    for (const fieldName of Object.keys(schema.fields)) {
	        for (const col of reserved) {
	            if (fieldName === col) {
	                throw new Error(`Field "${fieldName}" is a reserved tenancy column and should not be in the schema.`);
	            }
	        }
	    }
	    for (const attackField of ATTACK_FIELD_NAMES) {
	        const tree = (0, tenant_scoped_query_builder_1.flatFiltersToTree)([{ field: attackField, operator: 'equals', value: 'test' }]);
	        const { rejected } = (0, tenant_scoped_query_builder_1.buildTenantScopedWhere)(tenancy, schema, tree);
	        if (!rejected.includes(attackField)) {
	            throw new Error(`Attack field name "${attackField}" was not rejected. ` + `Expected it to appear in the rejected list.`);
	        }
	    }
	    for (const col of reserved) {
	        const tree = (0, tenant_scoped_query_builder_1.flatFiltersToTree)([{ field: col, operator: 'equals', value: 'attacker-value' }]);
	        const { rejected } = (0, tenant_scoped_query_builder_1.buildTenantScopedWhere)(tenancy, schema, tree);
	        if (!rejected.includes(col)) {
	            throw new Error(`Reserved column "${col}" was accepted as a filter field. ` + `It should be rejected by the field whitelist.`);
	        }
	    }
	    const schemaFields = Object.keys(schema.fields);
	    const field1 = schemaFields[0];
	    if (!field1) {
	        throw new Error('Schema has no fields — cannot run OR-escape test.');
	    }
	    const field2 = schemaFields[1] ?? field1;
	    const tree = (0, tenant_scoped_query_builder_1.flatFiltersToTree)([
	        { field: field1, operator: 'equals', value: 'a' },
	        { field: field2, operator: 'equals', value: 'b' },
	    ], types_1.LOGIC_OPERATORS.OR);
	    const { where } = (0, tenant_scoped_query_builder_1.buildTenantScopedWhere)(tenancy, schema, tree);
	    if (!where.AND || !Array.isArray(where.AND)) {
	        throw new Error('OR filter output is not wrapped in top-level AND.');
	    }
	    const andArray = where.AND;
	    const firstClause = JSON.stringify(andArray[0]);
	    const tenancyStr = JSON.stringify(tenancy);
	    if (firstClause !== tenancyStr) {
	        throw new Error(`Tenancy clause is not first in AND array. ` + `Expected ${tenancyStr}, got ${firstClause}.`);
	    }
	}
	
	return filterSchemaTestUtils;
}

var zodHelpers = {};

var hasRequiredZodHelpers;

function requireZodHelpers () {
	if (hasRequiredZodHelpers) return zodHelpers;
	hasRequiredZodHelpers = 1;
	Object.defineProperty(zodHelpers, "__esModule", { value: true });
	zodHelpers.logicOperatorSchema = void 0;
	zodHelpers.createColumnFilterZodSchema = createColumnFilterZodSchema;
	zodHelpers.createSortZodSchema = createSortZodSchema;
	const zod_1 = /*@__PURE__*/ requireZod();
	const types_1 = requireTypes$6();
	zodHelpers.logicOperatorSchema = zod_1.z.enum([
	    types_1.LOGIC_OPERATORS.AND,
	    types_1.LOGIC_OPERATORS.OR,
	]);
	const NUMERIC_VALUE_OPERATORS = new Set([
	    'equals',
	    'doesNotEqual',
	    'is',
	    'not',
	    'greaterThan',
	    'greaterThanOrEqual',
	    'lessThan',
	    'lessThanOrEqual',
	]);
	const DATE_VALUE_OPERATORS = new Set([
	    'equals',
	    'doesNotEqual',
	    'is',
	    'not',
	    'greaterThan',
	    'greaterThanOrEqual',
	    'lessThan',
	    'lessThanOrEqual',
	]);
	function isParseableDate(value) {
	    const trimmed = value.trim();
	    const ms = /^-?\d+$/.test(trimmed) ? Number(trimmed) : Date.parse(trimmed);
	    return !Number.isNaN(ms);
	}
	function refineTypedValue(filter, ctx, valueOperators, isValid, expectation) {
	    if (valueOperators.has(filter.operator)) {
	        if (filter.value === undefined || filter.value === '') {
	            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Filter requires a value', path: ['value'] });
	            return;
	        }
	        if (!isValid(filter.value)) {
	            ctx.addIssue({
	                code: zod_1.z.ZodIssueCode.custom,
	                message: `Filter value is not ${expectation}: ${filter.value}`,
	                path: ['value'],
	            });
	        }
	    }
	    else if (filter.operator === 'isAnyOf' || filter.operator === 'isNoneOf') {
	        if (filter.value === undefined || filter.value === '') {
	            ctx.addIssue({
	                code: zod_1.z.ZodIssueCode.custom,
	                message: `${filter.operator} requires a value`,
	                path: ['value'],
	            });
	            return;
	        }
	        const parts = parseIsAnyOfForValidation(filter.value);
	        if (parts.length === 0) {
	            ctx.addIssue({
	                code: zod_1.z.ZodIssueCode.custom,
	                message: `${filter.operator} contains no valid entries`,
	                path: ['value'],
	            });
	            return;
	        }
	        for (const part of parts) {
	            if (!isValid(part)) {
	                ctx.addIssue({
	                    code: zod_1.z.ZodIssueCode.custom,
	                    message: `${filter.operator} contains an entry that is not ${expectation}: ${part}`,
	                    path: ['value'],
	                });
	            }
	        }
	    }
	}
	function createColumnFilterZodSchema(schema, options) {
	    const maxFilters = options?.maxFilters ?? 20;
	    const maxValueLength = options?.maxValueLength ?? 1000;
	    const allowedFields = [...Object.keys(schema.fields), ...(schema.ignoredFields ?? [])];
	    const fieldDescription = renderFieldLegend(schema, 'Filter field.');
	    const columnFilterItemSchema = zod_1.z
	        .object({
	        field: zod_1.z.enum(allowedFields).describe(fieldDescription),
	        operator: zod_1.z.enum(types_1.COLUMN_FILTER_OPERATORS),
	        value: zod_1.z.coerce.string().max(maxValueLength).optional(),
	    })
	        .superRefine((filter, ctx) => {
	        const fieldDef = schema.fields[filter.field];
	        if (!fieldDef) {
	            return;
	        }
	        if (fieldDef.options.type === types_1.FieldType.Number) {
	            refineTypedValue(filter, ctx, NUMERIC_VALUE_OPERATORS, (v) => Number.isFinite(Number(v)), 'a finite number');
	        }
	        else if (fieldDef.options.type === types_1.FieldType.Date) {
	            refineTypedValue(filter, ctx, DATE_VALUE_OPERATORS, isParseableDate, 'a valid date');
	        }
	    });
	    return zod_1.z
	        .array(columnFilterItemSchema)
	        .max(maxFilters)
	        .transform((filters) => filters.map((f) => {
	        const fieldDef = schema.fields[f.field];
	        if (!fieldDef || f.value === undefined) {
	            return f;
	        }
	        if ('values' in fieldDef.options && fieldDef.options.values) {
	            const dbValue = fieldDef.options.values[f.value];
	            if (dbValue !== undefined) {
	                return { ...f, value: dbValue };
	            }
	        }
	        return f;
	    }));
	}
	function parseIsAnyOfForValidation(value) {
	    try {
	        const parsed = JSON.parse(value);
	        if (Array.isArray(parsed)) {
	            return parsed
	                .filter((x) => {
	                const t = typeof x;
	                return t === 'string' || t === 'number' || t === 'boolean';
	            })
	                .map(String);
	        }
	        if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
	            return [String(parsed)];
	        }
	        return [];
	    }
	    catch {
	        return value
	            .split(',')
	            .map((v) => v.trim())
	            .filter(Boolean);
	    }
	}
	function createSortZodSchema(schema) {
	    const sortableFields = Object.keys(schema.fields);
	    const fieldDescription = renderFieldLegend(schema, 'Sort field.');
	    return zod_1.z.array(zod_1.z.object({
	        field: zod_1.z.enum(sortableFields).describe(fieldDescription),
	        direction: zod_1.z.enum(['asc', 'desc']),
	    }));
	}
	function renderFieldLegend(schema, prefix) {
	    const entries = Object.entries(schema.fields);
	    const anyDescribed = entries.some(([, def]) => def.description !== undefined && def.description !== '');
	    if (!anyDescribed) {
	        return prefix;
	    }
	    const lines = entries.map(([name, def]) => {
	        const desc = def.description;
	        return desc ? `- ${name}: ${desc}` : `- ${name}`;
	    });
	    return `${prefix} Fields:\n${lines.join('\n')}`;
	}
	
	return zodHelpers;
}

var inMemoryExecutor = {};

var hasRequiredInMemoryExecutor;

function requireInMemoryExecutor () {
	if (hasRequiredInMemoryExecutor) return inMemoryExecutor;
	hasRequiredInMemoryExecutor = 1;
	Object.defineProperty(inMemoryExecutor, "__esModule", { value: true });
	inMemoryExecutor.applyColumnFilters = applyColumnFilters;
	inMemoryExecutor.applySort = applySort;
	const operator_handlers_1 = requireOperatorHandlers();
	const types_1 = requireTypes$6();
	function applyColumnFilters(items, filters, schema, accessors, logicOperator = 'and') {
	    if (filters.length === 0) {
	        return items;
	    }
	    const ignored = new Set(schema.ignoredFields ?? []);
	    const predicates = [];
	    for (const filter of filters) {
	        if (ignored.has(filter.field)) {
	            continue;
	        }
	        if (!types_1.VALID_FIELD_NAME_PATTERN.test(filter.field)) {
	            continue;
	        }
	        if (!Object.hasOwn(schema.fields, filter.field)) {
	            continue;
	        }
	        const fieldDef = schema.fields[filter.field];
	        if (fieldDef.allowedOperators && !fieldDef.allowedOperators.includes(filter.operator)) {
	            continue;
	        }
	        const accessor = accessors[filter.field];
	        if (!accessor) {
	            continue;
	        }
	        const predicate = buildPredicate(fieldDef, filter.operator, filter.value, accessor);
	        if (predicate) {
	            predicates.push(predicate);
	        }
	    }
	    if (predicates.length === 0) {
	        return items;
	    }
	    if (logicOperator === 'or') {
	        return items.filter((item) => predicates.some((p) => p(item)));
	    }
	    return items.filter((item) => predicates.every((p) => p(item)));
	}
	function applySort(items, sort, schema, accessors) {
	    if (!sort || sort.length === 0) {
	        return items;
	    }
	    const validSorts = [];
	    for (const s of sort) {
	        if (!types_1.VALID_FIELD_NAME_PATTERN.test(s.field)) {
	            continue;
	        }
	        if (!Object.hasOwn(schema.fields, s.field)) {
	            continue;
	        }
	        const fieldDef = schema.fields[s.field];
	        if (fieldDef.options.type === types_1.FieldType.Relation || fieldDef.options.type === types_1.FieldType.Custom) {
	            continue;
	        }
	        const accessor = accessors[s.field];
	        if (!accessor) {
	            continue;
	        }
	        const direction = s.direction === 'desc' ? -1 : 1;
	        validSorts.push({ accessor, direction });
	    }
	    if (validSorts.length === 0) {
	        return items;
	    }
	    return [...items].sort((a, b) => {
	        for (const { accessor, direction } of validSorts) {
	            const av = accessor(a);
	            const bv = accessor(b);
	            const cmp = compareSortValues(av, bv);
	            if (cmp !== 0) {
	                return cmp * direction;
	            }
	        }
	        return 0;
	    });
	}
	function buildPredicate(fieldDef, operator, rawValue, accessor) {
	    const { options } = fieldDef;
	    try {
	        switch (options.type) {
	            case types_1.FieldType.String:
	                return buildStringPredicate(operator, rawValue, accessor, options.nonNullable);
	            case types_1.FieldType.Boolean:
	                return buildBooleanPredicate(operator, rawValue, accessor);
	            case types_1.FieldType.Number:
	                return buildNumberPredicate(operator, rawValue, accessor);
	            case types_1.FieldType.Date:
	                return buildDatePredicate(operator, rawValue, accessor);
	            case types_1.FieldType.Relation:
	            case types_1.FieldType.Custom:
	                return null;
	        }
	    }
	    catch {
	        return null;
	    }
	}
	function buildStringPredicate(operator, rawValue, accessor, nonNullable) {
	    switch (operator) {
	        case 'contains': {
	            if (rawValue === undefined) {
	                return null;
	            }
	            const lower = rawValue.toLowerCase();
	            return (item) => someString(accessor(item), (s) => s.toLowerCase().includes(lower));
	        }
	        case 'doesNotContain': {
	            if (rawValue === undefined) {
	                return null;
	            }
	            const lower = rawValue.toLowerCase();
	            return (item) => !someString(accessor(item), (s) => s.toLowerCase().includes(lower));
	        }
	        case 'equals':
	        case 'is': {
	            if (rawValue === undefined) {
	                return null;
	            }
	            return (item) => someString(accessor(item), (s) => s === rawValue);
	        }
	        case 'doesNotEqual':
	        case 'not': {
	            if (rawValue === undefined) {
	                return null;
	            }
	            return (item) => !someString(accessor(item), (s) => s === rawValue);
	        }
	        case 'startsWith': {
	            if (rawValue === undefined) {
	                return null;
	            }
	            const lower = rawValue.toLowerCase();
	            return (item) => someString(accessor(item), (s) => s.toLowerCase().startsWith(lower));
	        }
	        case 'endsWith': {
	            if (rawValue === undefined) {
	                return null;
	            }
	            const lower = rawValue.toLowerCase();
	            return (item) => someString(accessor(item), (s) => s.toLowerCase().endsWith(lower));
	        }
	        case 'isEmpty':
	            return (item) => {
	                const v = accessor(item);
	                if (v === undefined || v === null) {
	                    return !nonNullable;
	                }
	                if (Array.isArray(v)) {
	                    return v.length === 0 || v.every((s) => s === '');
	                }
	                return v === '';
	            };
	        case 'isNotEmpty':
	            return (item) => {
	                const v = accessor(item);
	                if (v === undefined || v === null) {
	                    return false;
	                }
	                if (Array.isArray(v)) {
	                    return v.some((s) => s !== '' && s !== null && s !== undefined);
	                }
	                return v !== '';
	            };
	        case 'isAnyOf': {
	            const values = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
	            const set = new Set(values);
	            return (item) => someString(accessor(item), (s) => set.has(s));
	        }
	        case 'isNoneOf': {
	            const values = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
	            const set = new Set(values);
	            return (item) => !someString(accessor(item), (s) => set.has(s));
	        }
	        default:
	            return null;
	    }
	}
	function buildBooleanPredicate(operator, rawValue, accessor) {
	    const known = (item) => {
	        const value = accessor(item);
	        if (typeof value === 'boolean') {
	            return value;
	        }
	        if (value === 'true' || value === 'false') {
	            return value === 'true';
	        }
	        return null;
	    };
	    switch (operator) {
	        case 'equals':
	        case 'is': {
	            const target = rawValue === 'true';
	            return (item) => known(item) === target;
	        }
	        case 'doesNotEqual':
	        case 'not': {
	            const target = rawValue === 'true';
	            return (item) => {
	                const value = known(item);
	                return value !== null && value !== target;
	            };
	        }
	        default:
	            return null;
	    }
	}
	function buildNumberPredicate(operator, rawValue, accessor) {
	    switch (operator) {
	        case 'equals':
	        case 'is': {
	            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
	            return (item) => someNumber(accessor(item), (x) => x === n);
	        }
	        case 'doesNotEqual':
	        case 'not': {
	            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
	            return (item) => !someNumber(accessor(item), (x) => x === n);
	        }
	        case 'greaterThan': {
	            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
	            return (item) => someNumber(accessor(item), (x) => x > n);
	        }
	        case 'greaterThanOrEqual': {
	            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
	            return (item) => someNumber(accessor(item), (x) => x >= n);
	        }
	        case 'lessThan': {
	            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
	            return (item) => someNumber(accessor(item), (x) => x < n);
	        }
	        case 'lessThanOrEqual': {
	            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
	            return (item) => someNumber(accessor(item), (x) => x <= n);
	        }
	        case 'isEmpty':
	            return (item) => {
	                const v = accessor(item);
	                return v === null || v === undefined;
	            };
	        case 'isNotEmpty':
	            return (item) => {
	                const v = accessor(item);
	                return v !== null && v !== undefined;
	            };
	        case 'isAnyOf': {
	            const raw = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
	            const numbers = raw.map((item) => {
	                const n = Number(item);
	                if (!Number.isFinite(n)) {
	                    throw new Error(`Numeric isAnyOf received non-numeric entry: ${item}`);
	                }
	                return n;
	            });
	            const set = new Set(numbers);
	            return (item) => someNumber(accessor(item), (x) => set.has(x));
	        }
	        case 'isNoneOf': {
	            const raw = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
	            const numbers = raw.map((item) => {
	                const n = Number(item);
	                if (!Number.isFinite(n)) {
	                    throw new Error(`Numeric isNoneOf received non-numeric entry: ${item}`);
	                }
	                return n;
	            });
	            const set = new Set(numbers);
	            return (item) => !someNumber(accessor(item), (x) => set.has(x));
	        }
	        default:
	            return null;
	    }
	}
	function buildDatePredicate(operator, rawValue, accessor) {
	    switch (operator) {
	        case 'equals':
	        case 'is': {
	            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
	            return (item) => someDate(accessor(item), (x) => x === t);
	        }
	        case 'doesNotEqual':
	        case 'not': {
	            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
	            return (item) => !someDate(accessor(item), (x) => x === t);
	        }
	        case 'greaterThan': {
	            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
	            return (item) => someDate(accessor(item), (x) => x > t);
	        }
	        case 'greaterThanOrEqual': {
	            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
	            return (item) => someDate(accessor(item), (x) => x >= t);
	        }
	        case 'lessThan': {
	            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
	            return (item) => someDate(accessor(item), (x) => x < t);
	        }
	        case 'lessThanOrEqual': {
	            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
	            return (item) => someDate(accessor(item), (x) => x <= t);
	        }
	        case 'isEmpty':
	            return (item) => {
	                const v = accessor(item);
	                return v === null || v === undefined;
	            };
	        case 'isNotEmpty':
	            return (item) => {
	                const v = accessor(item);
	                return v !== null && v !== undefined;
	            };
	        case 'isAnyOf': {
	            const times = new Set((0, operator_handlers_1.parseIsAnyOfValues)(rawValue).map((v) => (0, operator_handlers_1.parseDateValue)(v).getTime()));
	            return (item) => someDate(accessor(item), (x) => times.has(x));
	        }
	        case 'isNoneOf': {
	            const times = new Set((0, operator_handlers_1.parseIsAnyOfValues)(rawValue).map((v) => (0, operator_handlers_1.parseDateValue)(v).getTime()));
	            return (item) => !someDate(accessor(item), (x) => times.has(x));
	        }
	        default:
	            return null;
	    }
	}
	function someString(value, predicate) {
	    if (value === null || value === undefined) {
	        return false;
	    }
	    if (Array.isArray(value)) {
	        return value.some((v) => typeof v === 'string' && predicate(v));
	    }
	    if (typeof value !== 'string') {
	        return false;
	    }
	    return predicate(value);
	}
	function someNumber(value, predicate) {
	    if (value === null || value === undefined) {
	        return false;
	    }
	    if (Array.isArray(value)) {
	        return value.some((v) => typeof v === 'number' && predicate(v));
	    }
	    if (typeof value !== 'number') {
	        return false;
	    }
	    return predicate(value);
	}
	function someDate(value, predicate) {
	    if (value === null || value === undefined) {
	        return false;
	    }
	    const apply = (v) => {
	        const ms = typeof v === 'number' ? v
	            : /^-?\d+$/.test(v.trim()) ? Number(v)
	                : Date.parse(v);
	        return !Number.isNaN(ms) && predicate(ms);
	    };
	    if (Array.isArray(value)) {
	        return value.some((v) => (typeof v === 'string' || typeof v === 'number') && apply(v));
	    }
	    if (typeof value !== 'string' && typeof value !== 'number') {
	        return false;
	    }
	    return apply(value);
	}
	function compareSortValues(a, b) {
	    const aEmpty = a === null || a === undefined;
	    const bEmpty = b === null || b === undefined;
	    if (aEmpty && bEmpty) {
	        return 0;
	    }
	    if (aEmpty) {
	        return 1;
	    }
	    if (bEmpty) {
	        return -1;
	    }
	    const av = Array.isArray(a) ? (a[0] ?? null) : a;
	    const bv = Array.isArray(b) ? (b[0] ?? null) : b;
	    if (av === null || av === undefined) {
	        return bv === null || bv === undefined ? 0 : 1;
	    }
	    if (bv === null || bv === undefined) {
	        return -1;
	    }
	    if (typeof av === 'number' && typeof bv === 'number') {
	        return (av < bv ? -1
	            : av > bv ? 1
	                : 0);
	    }
	    if (typeof av === 'boolean' && typeof bv === 'boolean') {
	        return (av === bv ? 0
	            : av ? 1
	                : -1);
	    }
	    const as = String(av);
	    const bs = String(bv);
	    return (as < bs ? -1
	        : as > bs ? 1
	            : 0);
	}
	
	return inMemoryExecutor;
}

var inMemoryPagination = {};

var hasRequiredInMemoryPagination;

function requireInMemoryPagination () {
	if (hasRequiredInMemoryPagination) return inMemoryPagination;
	hasRequiredInMemoryPagination = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.MAX_PAGE_LIMIT = exports.DEFAULT_PAGE_LIMIT = void 0;
		exports.applySortedFilter = applySortedFilter;
		exports.paginate = paginate;
		const in_memory_executor_1 = requireInMemoryExecutor();
		exports.DEFAULT_PAGE_LIMIT = 50;
		exports.MAX_PAGE_LIMIT = 500;
		function applySortedFilter(entries, filters, sort, logicOperator, schema, accessors) {
		    const filtered = (0, in_memory_executor_1.applyColumnFilters)(entries, filters ?? [], schema, accessors, logicOperator);
		    const sorted = sort && sort.length > 0 ? (0, in_memory_executor_1.applySort)(filtered, sort, schema, accessors) : filtered;
		    return sorted;
		}
		function paginate(items, cursor, limit, getKey) {
		    const take = Math.min(Math.max(limit ?? exports.DEFAULT_PAGE_LIMIT, 1), exports.MAX_PAGE_LIMIT);
		    let start = 0;
		    if (cursor !== undefined && cursor !== '') {
		        const idx = items.findIndex((it) => getKey(it) === cursor);
		        if (idx < 0) {
		            return { items: [], endCursor: null, hasNextPage: false, totalCount: items.length };
		        }
		        start = idx + 1;
		    }
		    const slice = items.slice(start, start + take + 1);
		    const hasNextPage = slice.length > take;
		    const page = hasNextPage ? slice.slice(0, take) : slice;
		    const endCursor = page.length > 0 ? getKey(page[page.length - 1]) : null;
		    return {
		        items: page,
		        endCursor,
		        hasNextPage,
		        totalCount: items.length,
		    };
		}
		
	} (inMemoryPagination));
	return inMemoryPagination;
}

var hasRequiredQueryBuilder;

function requireQueryBuilder () {
	if (hasRequiredQueryBuilder) return QueryBuilder;
	hasRequiredQueryBuilder = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.MAX_PAGE_LIMIT = exports.DEFAULT_PAGE_LIMIT = exports.paginate = exports.applySortedFilter = exports.applySort = exports.applyColumnFilters = exports.logicOperatorSchema = exports.createSortZodSchema = exports.createColumnFilterZodSchema = exports.assertFilterSchemaSecurity = exports.parseNumberValue = exports.parseIsAnyOfValues = exports.parseDateValue = exports.buildStringOperatorClause = exports.buildRelationOperatorClause = exports.buildNumberOperatorClause = exports.buildDateOperatorClause = exports.buildBooleanOperatorClause = exports.flatFiltersToTree = exports.buildTenantScopedWhere = exports.buildTenantScopedOrderBy = exports.createFilterSchema = exports.VALID_FIELD_NAME_PATTERN = exports.SortDirection = exports.LOGIC_OPERATORS = exports.FieldType = exports.DEFAULT_RESERVED_COLUMNS = exports.COLUMN_FILTER_OPERATORS = void 0;
		var types_1 = requireTypes$6();
		Object.defineProperty(exports, "COLUMN_FILTER_OPERATORS", { enumerable: true, get: function () { return types_1.COLUMN_FILTER_OPERATORS; } });
		Object.defineProperty(exports, "DEFAULT_RESERVED_COLUMNS", { enumerable: true, get: function () { return types_1.DEFAULT_RESERVED_COLUMNS; } });
		Object.defineProperty(exports, "FieldType", { enumerable: true, get: function () { return types_1.FieldType; } });
		Object.defineProperty(exports, "LOGIC_OPERATORS", { enumerable: true, get: function () { return types_1.LOGIC_OPERATORS; } });
		Object.defineProperty(exports, "SortDirection", { enumerable: true, get: function () { return types_1.SortDirection; } });
		Object.defineProperty(exports, "VALID_FIELD_NAME_PATTERN", { enumerable: true, get: function () { return types_1.VALID_FIELD_NAME_PATTERN; } });
		var filter_schema_1 = requireFilterSchema();
		Object.defineProperty(exports, "createFilterSchema", { enumerable: true, get: function () { return filter_schema_1.createFilterSchema; } });
		var tenant_scoped_query_builder_1 = requireTenantScopedQueryBuilder();
		Object.defineProperty(exports, "buildTenantScopedOrderBy", { enumerable: true, get: function () { return tenant_scoped_query_builder_1.buildTenantScopedOrderBy; } });
		Object.defineProperty(exports, "buildTenantScopedWhere", { enumerable: true, get: function () { return tenant_scoped_query_builder_1.buildTenantScopedWhere; } });
		Object.defineProperty(exports, "flatFiltersToTree", { enumerable: true, get: function () { return tenant_scoped_query_builder_1.flatFiltersToTree; } });
		var operator_handlers_1 = requireOperatorHandlers();
		Object.defineProperty(exports, "buildBooleanOperatorClause", { enumerable: true, get: function () { return operator_handlers_1.buildBooleanOperatorClause; } });
		Object.defineProperty(exports, "buildDateOperatorClause", { enumerable: true, get: function () { return operator_handlers_1.buildDateOperatorClause; } });
		Object.defineProperty(exports, "buildNumberOperatorClause", { enumerable: true, get: function () { return operator_handlers_1.buildNumberOperatorClause; } });
		Object.defineProperty(exports, "buildRelationOperatorClause", { enumerable: true, get: function () { return operator_handlers_1.buildRelationOperatorClause; } });
		Object.defineProperty(exports, "buildStringOperatorClause", { enumerable: true, get: function () { return operator_handlers_1.buildStringOperatorClause; } });
		Object.defineProperty(exports, "parseDateValue", { enumerable: true, get: function () { return operator_handlers_1.parseDateValue; } });
		Object.defineProperty(exports, "parseIsAnyOfValues", { enumerable: true, get: function () { return operator_handlers_1.parseIsAnyOfValues; } });
		Object.defineProperty(exports, "parseNumberValue", { enumerable: true, get: function () { return operator_handlers_1.parseNumberValue; } });
		var filter_schema_test_utils_1 = requireFilterSchemaTestUtils();
		Object.defineProperty(exports, "assertFilterSchemaSecurity", { enumerable: true, get: function () { return filter_schema_test_utils_1.assertFilterSchemaSecurity; } });
		var zod_helpers_1 = requireZodHelpers();
		Object.defineProperty(exports, "createColumnFilterZodSchema", { enumerable: true, get: function () { return zod_helpers_1.createColumnFilterZodSchema; } });
		Object.defineProperty(exports, "createSortZodSchema", { enumerable: true, get: function () { return zod_helpers_1.createSortZodSchema; } });
		Object.defineProperty(exports, "logicOperatorSchema", { enumerable: true, get: function () { return zod_helpers_1.logicOperatorSchema; } });
		var in_memory_executor_1 = requireInMemoryExecutor();
		Object.defineProperty(exports, "applyColumnFilters", { enumerable: true, get: function () { return in_memory_executor_1.applyColumnFilters; } });
		Object.defineProperty(exports, "applySort", { enumerable: true, get: function () { return in_memory_executor_1.applySort; } });
		var in_memory_pagination_1 = requireInMemoryPagination();
		Object.defineProperty(exports, "applySortedFilter", { enumerable: true, get: function () { return in_memory_pagination_1.applySortedFilter; } });
		Object.defineProperty(exports, "paginate", { enumerable: true, get: function () { return in_memory_pagination_1.paginate; } });
		Object.defineProperty(exports, "DEFAULT_PAGE_LIMIT", { enumerable: true, get: function () { return in_memory_pagination_1.DEFAULT_PAGE_LIMIT; } });
		Object.defineProperty(exports, "MAX_PAGE_LIMIT", { enumerable: true, get: function () { return in_memory_pagination_1.MAX_PAGE_LIMIT; } });
		
	} (QueryBuilder));
	return QueryBuilder;
}

var hasRequiredFilterSchemas$2;

function requireFilterSchemas$2 () {
	if (hasRequiredFilterSchemas$2) return filterSchemas$2;
	hasRequiredFilterSchemas$2 = 1;
	Object.defineProperty(filterSchemas$2, "__esModule", { value: true });
	filterSchemas$2.agentChatConversationFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	filterSchemas$2.agentChatConversationFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        agentId: {
	            description: 'Agent identifier the conversation is bound to (e.g. cadstrom-project-assistant).',
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	        },
	        sessionId: {
	            description: 'Client-supplied session identifier captured at conversation creation.',
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	        },
	        title: {
	            description: 'Human-readable conversation title. Null until generated.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        lastActivityAt: {
	            description: 'Timestamp of the most recent activity. Sort-only.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: () => ({}),
	                buildOrderBy: (direction) => [{ lastActivityAt: direction }],
	            },
	            allowedOperators: [],
	        },
	        createdAt: {
	            description: 'Timestamp the conversation row was created. Sort-only.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: () => ({}),
	                buildOrderBy: (direction) => [{ createdAt: direction }],
	            },
	            allowedOperators: [],
	        },
	    },
	});
	
	return filterSchemas$2;
}

var ingestNarration = {};

var hasRequiredIngestNarration;

function requireIngestNarration () {
	if (hasRequiredIngestNarration) return ingestNarration;
	hasRequiredIngestNarration = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ingestNarrationDataPartSchemas = exports.ingestBomReviewCtaDataSchema = exports.ingestChecklistEstimateFileSchema = exports.ingestChecklistEstimateCategorySchema = exports.INGEST_CHECKLIST_ESTIMATE_SCHEMA_VERSION = exports.ingestValidationChecklistDataSchema = exports.ingestValidationCategorySchema = exports.IngestValidationCategoryStatus = exports.ingestEngineBadgeSchema = exports.IngestEngineKind = exports.ingestActivityDataSchema = exports.ingestActivityLineSchema = exports.IngestActivityStatus = exports.EXPLORATION_TERMINAL_ROW_IDS = exports.EXPLORATION_FALLBACK_ROW_ID = exports.EXPLORATION_COMPLETION_ROW_ID = exports.ingestTaskListDataSchema = exports.ingestTaskItemSchema = exports.IngestTaskStatus = exports.ingestStateDataSchema = exports.IngestNarrationPhase = exports.INGEST_NARRATION_STATE_PART_TYPE = exports.INGEST_NARRATION_FINALE_MESSAGE_ID = exports.INGEST_NARRATION_OPENING_MESSAGE_ID = void 0;
		const z = /*@__PURE__*/ requireZod();
		exports.INGEST_NARRATION_OPENING_MESSAGE_ID = 'msg-ingest-narration-opening';
		exports.INGEST_NARRATION_FINALE_MESSAGE_ID = 'msg-ingest-narration-finale';
		exports.INGEST_NARRATION_STATE_PART_TYPE = 'data-ingest-state';
		var IngestNarrationPhase;
		(function (IngestNarrationPhase) {
		    IngestNarrationPhase["Narrating"] = "narrating";
		    IngestNarrationPhase["Complete"] = "complete";
		})(IngestNarrationPhase || (exports.IngestNarrationPhase = IngestNarrationPhase = {}));
		exports.ingestStateDataSchema = z.object({
		    phase: z.nativeEnum(IngestNarrationPhase),
		    openingSettled: z.boolean().optional(),
		});
		var IngestTaskStatus;
		(function (IngestTaskStatus) {
		    IngestTaskStatus["Pending"] = "pending";
		    IngestTaskStatus["Active"] = "active";
		    IngestTaskStatus["Done"] = "done";
		})(IngestTaskStatus || (exports.IngestTaskStatus = IngestTaskStatus = {}));
		exports.ingestTaskItemSchema = z.object({
		    id: z.string(),
		    label: z.string(),
		    status: z.nativeEnum(IngestTaskStatus),
		    detail: z.string().optional(),
		});
		exports.ingestTaskListDataSchema = z.object({
		    title: z.string().optional(),
		    tasks: z.array(exports.ingestTaskItemSchema),
		});
		exports.EXPLORATION_COMPLETION_ROW_ID = 'exploration-complete';
		exports.EXPLORATION_FALLBACK_ROW_ID = 'exploration-fallback';
		exports.EXPLORATION_TERMINAL_ROW_IDS = [
		    exports.EXPLORATION_COMPLETION_ROW_ID,
		    exports.EXPLORATION_FALLBACK_ROW_ID,
		];
		var IngestActivityStatus;
		(function (IngestActivityStatus) {
		    IngestActivityStatus["Running"] = "running";
		    IngestActivityStatus["Done"] = "done";
		    IngestActivityStatus["Error"] = "error";
		})(IngestActivityStatus || (exports.IngestActivityStatus = IngestActivityStatus = {}));
		exports.ingestActivityLineSchema = z.object({
		    id: z.string(),
		    label: z.string(),
		    detail: z.string().optional(),
		    status: z.nativeEnum(IngestActivityStatus),
		});
		exports.ingestActivityDataSchema = z.object({
		    lines: z.array(exports.ingestActivityLineSchema),
		});
		var IngestEngineKind;
		(function (IngestEngineKind) {
		    IngestEngineKind["Engine"] = "engine";
		    IngestEngineKind["Ai"] = "ai";
		})(IngestEngineKind || (exports.IngestEngineKind = IngestEngineKind = {}));
		exports.ingestEngineBadgeSchema = z.object({
		    label: z.string(),
		    kind: z.nativeEnum(IngestEngineKind),
		});
		var IngestValidationCategoryStatus;
		(function (IngestValidationCategoryStatus) {
		    IngestValidationCategoryStatus["Pending"] = "pending";
		    IngestValidationCategoryStatus["Counting"] = "counting";
		    IngestValidationCategoryStatus["Queued"] = "queued";
		})(IngestValidationCategoryStatus || (exports.IngestValidationCategoryStatus = IngestValidationCategoryStatus = {}));
		exports.ingestValidationCategorySchema = z.object({
		    id: z.string(),
		    label: z.string(),
		    engines: z.array(exports.ingestEngineBadgeSchema),
		    valueText: z.string().nullable(),
		    status: z.nativeEnum(IngestValidationCategoryStatus),
		});
		exports.ingestValidationChecklistDataSchema = z.object({
		    title: z.string(),
		    totalText: z.string(),
		    totalCaption: z.string(),
		    categories: z.array(exports.ingestValidationCategorySchema),
		});
		exports.INGEST_CHECKLIST_ESTIMATE_SCHEMA_VERSION = 1;
		exports.ingestChecklistEstimateCategorySchema = z.object({
		    id: z.string(),
		    label: z.string(),
		    engines: z.array(exports.ingestEngineBadgeSchema),
		    count: z.number().int().nonnegative().nullable(),
		    valueText: z.string(),
		});
		exports.ingestChecklistEstimateFileSchema = z.object({
		    schemaVersion: z.literal(exports.INGEST_CHECKLIST_ESTIMATE_SCHEMA_VERSION),
		    projectModelId: z.string(),
		    generatedAt: z.string(),
		    categories: z.array(exports.ingestChecklistEstimateCategorySchema),
		    total: z.number().int().nonnegative(),
		});
		exports.ingestBomReviewCtaDataSchema = z.object({
		    headline: z.string(),
		    body: z.string(),
		    buttonLabel: z.string(),
		});
		exports.ingestNarrationDataPartSchemas = {
		    'ingest-state': exports.ingestStateDataSchema,
		    'ingest-task-list': exports.ingestTaskListDataSchema,
		    'ingest-activity': exports.ingestActivityDataSchema,
		    'ingest-validation-checklist': exports.ingestValidationChecklistDataSchema,
		    'ingest-bom-review-cta': exports.ingestBomReviewCtaDataSchema,
		};
		
	} (ingestNarration));
	return ingestNarration;
}

var toolCallAnnotations = {};

var hasRequiredToolCallAnnotations;

function requireToolCallAnnotations () {
	if (hasRequiredToolCallAnnotations) return toolCallAnnotations;
	hasRequiredToolCallAnnotations = 1;
	Object.defineProperty(toolCallAnnotations, "__esModule", { value: true });
	toolCallAnnotations.buildToolMetadata = buildToolMetadata;
	toolCallAnnotations.collectToolCallAnnotations = collectToolCallAnnotations;
	toolCallAnnotations.readToolCallAnnotations = readToolCallAnnotations;
	function buildToolMetadata(annotations) {
	    const metadata = {
	        ...(annotations.detail === undefined ? {} : { detail: annotations.detail }),
	        ...(annotations.displayName === undefined ? {} : { displayName: annotations.displayName }),
	    };
	    return Object.keys(metadata).length === 0 ? undefined : metadata;
	}
	function collectToolCallAnnotations(messages) {
	    const annotations = new Map();
	    for (const message of messages) {
	        if (!isRecord(message) || !Array.isArray(message['parts'])) {
	            continue;
	        }
	        for (const part of message['parts']) {
	            if (!isRecord(part)) {
	                continue;
	            }
	            const toolCallId = part['toolCallId'];
	            const parsed = isRecord(part['toolMetadata']) ? readToolCallAnnotations(part['toolMetadata']) : undefined;
	            if (typeof toolCallId !== 'string' || parsed === undefined) {
	                continue;
	            }
	            annotations.set(toolCallId, parsed);
	        }
	    }
	    return annotations;
	}
	function readToolCallAnnotations(metadata) {
	    const detail = readAnnotationString(metadata['detail']);
	    const displayName = readAnnotationString(metadata['displayName']);
	    if (detail === undefined && displayName === undefined) {
	        return undefined;
	    }
	    return {
	        ...(detail === undefined ? {} : { detail }),
	        ...(displayName === undefined ? {} : { displayName }),
	    };
	}
	function readAnnotationString(value) {
	    return typeof value === 'string' && value.length > 0 ? value : undefined;
	}
	function isRecord(value) {
	    return typeof value === 'object' && value !== null;
	}
	
	return toolCallAnnotations;
}

var hasRequiredAgentChat;

function requireAgentChat () {
	if (hasRequiredAgentChat) return AgentChat;
	hasRequiredAgentChat = 1;
	(function (exports) {
		var __createBinding = (AgentChat && AgentChat.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (AgentChat && AgentChat.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireChatSurfaceAgents(), exports);
		__exportStar(requireFilterSchemas$2(), exports);
		__exportStar(requireIngestNarration(), exports);
		__exportStar(requireToolCallAnnotations(), exports);
		
	} (AgentChat));
	return AgentChat;
}

var AgenticExtraction = {};

var agenticExtractorRegistry = {};

var hasRequiredAgenticExtractorRegistry;

function requireAgenticExtractorRegistry () {
	if (hasRequiredAgenticExtractorRegistry) return agenticExtractorRegistry;
	hasRequiredAgenticExtractorRegistry = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AGENTIC_EXTRACTORS = exports.AgenticExtractorName = void 0;
		exports.agenticExtractorUserId = agenticExtractorUserId;
		exports.agenticExtractorUsername = agenticExtractorUsername;
		exports.agenticExtractorDisplayName = agenticExtractorDisplayName;
		exports.agenticExtractionBlobKey = agenticExtractionBlobKey;
		var AgenticExtractorName;
		(function (AgenticExtractorName) {
		    AgenticExtractorName["Ade"] = "ade";
		    AgenticExtractorName["Lde"] = "lde";
		    AgenticExtractorName["Pu"] = "pu";
		})(AgenticExtractorName || (exports.AgenticExtractorName = AgenticExtractorName = {}));
		exports.AGENTIC_EXTRACTORS = {
		    [AgenticExtractorName.Ade]: {
		        displayName: 'Agentic Datasheet Extraction',
		        currentVersion: 1,
		    },
		    [AgenticExtractorName.Lde]: {
		        displayName: 'Locke Data Extraction',
		        currentVersion: 1,
		    },
		    [AgenticExtractorName.Pu]: {
		        displayName: 'Package Understanding Workflow',
		        currentVersion: 1,
		    },
		};
		const USER_ID_VERSION_DIGITS = 4;
		const MAX_ENCODABLE_VERSION = 10 ** USER_ID_VERSION_DIGITS - 1;
		function assertValidVersion(version) {
		    if (!Number.isInteger(version) || version < 1) {
		        throw new Error(`Invalid agentic extractor version: ${version}`);
		    }
		}
		function agenticExtractorUserId(name, version) {
		    assertValidVersion(version);
		    if (version > MAX_ENCODABLE_VERSION) {
		        throw new Error(`Agentic extractor version ${version} exceeds the id's ${USER_ID_VERSION_DIGITS}-digit field`);
		    }
		    const versionField = String(version).padStart(USER_ID_VERSION_DIGITS, '0');
		    const suffix = `${name}v${versionField}`.toLowerCase().replace(/[^a-z0-9]/g, '');
		    return `u_${suffix.padEnd(32, '0')}`;
		}
		function agenticExtractorUsername(name, version) {
		    assertValidVersion(version);
		    return `${name}-v${version}`;
		}
		function agenticExtractorDisplayName(name, version) {
		    assertValidVersion(version);
		    return `${exports.AGENTIC_EXTRACTORS[name].displayName} v${version}`;
		}
		function agenticExtractionBlobKey(packageId, name, version, runId) {
		    assertValidVersion(version);
		    return `packages/${packageId}/${name}/v${version}/${runId}/result.json`;
		}
		
	} (agenticExtractorRegistry));
	return agenticExtractorRegistry;
}

var agenticExtractionResult = {};

var hasRequiredAgenticExtractionResult;

function requireAgenticExtractionResult () {
	if (hasRequiredAgenticExtractionResult) return agenticExtractionResult;
	hasRequiredAgenticExtractionResult = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AgenticExtractionResultSchema = exports.ExtractorEventKind = exports.AgenticExtractionRunStatus = exports.AGENTIC_EXTRACTION_RESULT_SCHEMA_VERSION = void 0;
		exports.parseAgenticExtractionResult = parseAgenticExtractionResult;
		const z = /*@__PURE__*/ requireZod();
		const agentic_extractor_registry_1 = requireAgenticExtractorRegistry();
		exports.AGENTIC_EXTRACTION_RESULT_SCHEMA_VERSION = 1;
		var AgenticExtractionRunStatus;
		(function (AgenticExtractionRunStatus) {
		    AgenticExtractionRunStatus["Emitted"] = "emitted";
		    AgenticExtractionRunStatus["Withheld"] = "withheld";
		})(AgenticExtractionRunStatus || (exports.AgenticExtractionRunStatus = AgenticExtractionRunStatus = {}));
		var ExtractorEventKind;
		(function (ExtractorEventKind) {
		    ExtractorEventKind["Started"] = "started";
		    ExtractorEventKind["Completed"] = "completed";
		    ExtractorEventKind["Failed"] = "failed";
		})(ExtractorEventKind || (exports.ExtractorEventKind = ExtractorEventKind = {}));
		exports.AgenticExtractionResultSchema = z.object({
		    schemaVersion: z.number(),
		    packageId: z.string(),
		    extractorName: z.nativeEnum(agentic_extractor_registry_1.AgenticExtractorName),
		    extractorVersion: z.number().int().min(1),
		    createdAt: z.number(),
		    sigmaModelType: z.string(),
		    resolution: z.unknown().optional(),
		});
		function parseAgenticExtractionResult(blob) {
		    const parsed = exports.AgenticExtractionResultSchema.parse(blob);
		    if (parsed.schemaVersion !== exports.AGENTIC_EXTRACTION_RESULT_SCHEMA_VERSION) {
		        throw new Error(`Unsupported agentic extraction result schemaVersion: ${parsed.schemaVersion}`);
		    }
		    return parsed;
		}
		
	} (agenticExtractionResult));
	return agenticExtractionResult;
}

var hasRequiredAgenticExtraction;

function requireAgenticExtraction () {
	if (hasRequiredAgenticExtraction) return AgenticExtraction;
	hasRequiredAgenticExtraction = 1;
	(function (exports) {
		var __createBinding = (AgenticExtraction && AgenticExtraction.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (AgenticExtraction && AgenticExtraction.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAgenticExtractorRegistry(), exports);
		__exportStar(requireAgenticExtractionResult(), exports);
		
	} (AgenticExtraction));
	return AgenticExtraction;
}

var Analysis$1 = {};

var agentDefinition = {};

var hasRequiredAgentDefinition;

function requireAgentDefinition () {
	if (hasRequiredAgentDefinition) return agentDefinition;
	hasRequiredAgentDefinition = 1;
	Object.defineProperty(agentDefinition, "__esModule", { value: true });
	agentDefinition.AnalysisAssertionDefinitionRefSchema = agentDefinition.AnalysisAgentTypeSchema = agentDefinition.AnalysisAgentType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var AnalysisAgentType;
	(function (AnalysisAgentType) {
	    AnalysisAgentType["Validation"] = "validation";
	    AnalysisAgentType["Finalization"] = "finalization";
	    AnalysisAgentType["SubjectGeneration"] = "subject-generation";
	    AnalysisAgentType["TriggerFilter"] = "trigger-filter";
	})(AnalysisAgentType || (agentDefinition.AnalysisAgentType = AnalysisAgentType = {}));
	agentDefinition.AnalysisAgentTypeSchema = zod_1.default.nativeEnum(AnalysisAgentType);
	agentDefinition.AnalysisAssertionDefinitionRefSchema = zod_1.default.object({
	    assertionDefinitionId: zod_1.default.string(),
	    slug: zod_1.default.string(),
	});
	
	return agentDefinition;
}

var busSubject = {};

var hasRequiredBusSubject;

function requireBusSubject () {
	if (hasRequiredBusSubject) return busSubject;
	hasRequiredBusSubject = 1;
	Object.defineProperty(busSubject, "__esModule", { value: true });
	busSubject.BUS_CUSTOM_SUBJECT_TYPE_NAME = void 0;
	busSubject.BUS_CUSTOM_SUBJECT_TYPE_NAME = 'cadstrom.bus';
	
	return busSubject;
}

var customSubject = {};

var hasRequiredCustomSubject;

function requireCustomSubject () {
	if (hasRequiredCustomSubject) return customSubject;
	hasRequiredCustomSubject = 1;
	Object.defineProperty(customSubject, "__esModule", { value: true });
	customSubject.CustomSubjectSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	customSubject.CustomSubjectSchema = zod_1.default.object({
	    id: zod_1.default.string(),
	    buildId: zod_1.default.string(),
	    customTypeName: zod_1.default.string(),
	    subjectName: zod_1.default.string(),
	    description: zod_1.default.string(),
	    properties: zod_1.default.record(zod_1.default.string(), zod_1.default.unknown()).optional(),
	});
	
	return customSubject;
}

var diffPairSubject = {};

var hasRequiredDiffPairSubject;

function requireDiffPairSubject () {
	if (hasRequiredDiffPairSubject) return diffPairSubject;
	hasRequiredDiffPairSubject = 1;
	Object.defineProperty(diffPairSubject, "__esModule", { value: true });
	diffPairSubject.DIFF_PAIR_CUSTOM_SUBJECT_TYPE_NAME = void 0;
	diffPairSubject.DIFF_PAIR_CUSTOM_SUBJECT_TYPE_NAME = 'cadstrom.diff_pair';
	
	return diffPairSubject;
}

var agentExecutor = {};

var triggerExecutor = {};

var hasRequiredTriggerExecutor;

function requireTriggerExecutor () {
	if (hasRequiredTriggerExecutor) return triggerExecutor;
	hasRequiredTriggerExecutor = 1;
	Object.defineProperty(triggerExecutor, "__esModule", { value: true });
	triggerExecutor.TriggerExecutorSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	triggerExecutor.TriggerExecutorSchema = zod_1.default.object({
	    executorType: zod_1.default.string(),
	});
	
	return triggerExecutor;
}

var hasRequiredAgentExecutor;

function requireAgentExecutor () {
	if (hasRequiredAgentExecutor) return agentExecutor;
	hasRequiredAgentExecutor = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AgentExecutorSchema = exports.AGENT_EXECUTOR_TYPE = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const trigger_executor_1 = requireTriggerExecutor();
		exports.AGENT_EXECUTOR_TYPE = 'agent';
		exports.AgentExecutorSchema = trigger_executor_1.TriggerExecutorSchema.extend({
		    executorType: zod_1.default.literal(exports.AGENT_EXECUTOR_TYPE),
		    agentId: zod_1.default.string(),
		});
		
	} (agentExecutor));
	return agentExecutor;
}

var hybridExecutor = {};

var hasRequiredHybridExecutor;

function requireHybridExecutor () {
	if (hasRequiredHybridExecutor) return hybridExecutor;
	hasRequiredHybridExecutor = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.HybridExecutorSchema = exports.HYBRID_EXECUTOR_TYPE = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const trigger_executor_1 = requireTriggerExecutor();
		exports.HYBRID_EXECUTOR_TYPE = 'hybrid';
		exports.HybridExecutorSchema = trigger_executor_1.TriggerExecutorSchema.extend({
		    executorType: zod_1.default.literal(exports.HYBRID_EXECUTOR_TYPE),
		    orchestratorId: zod_1.default.string(),
		});
		
	} (hybridExecutor));
	return hybridExecutor;
}

var lifecycle = {};

var hasRequiredLifecycle;

function requireLifecycle () {
	if (hasRequiredLifecycle) return lifecycle;
	hasRequiredLifecycle = 1;
	Object.defineProperty(lifecycle, "__esModule", { value: true });
	lifecycle.AnalysisTriggerRunPolicySchema = lifecycle.AnalysisTriggerRunPolicy = lifecycle.AnalysisLifecycleStageSchema = lifecycle.AnalysisLifecycleStage = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var AnalysisLifecycleStage;
	(function (AnalysisLifecycleStage) {
	    AnalysisLifecycleStage["PackageUnderstanding"] = "package-understanding";
	    AnalysisLifecycleStage["PreAnalysis"] = "pre-analysis";
	    AnalysisLifecycleStage["Analysis"] = "analysis";
	    AnalysisLifecycleStage["PostAnalysis"] = "post-analysis";
	})(AnalysisLifecycleStage || (lifecycle.AnalysisLifecycleStage = AnalysisLifecycleStage = {}));
	lifecycle.AnalysisLifecycleStageSchema = zod_1.default.nativeEnum(AnalysisLifecycleStage);
	var AnalysisTriggerRunPolicy;
	(function (AnalysisTriggerRunPolicy) {
	    AnalysisTriggerRunPolicy["Automatic"] = "automatic";
	    AnalysisTriggerRunPolicy["OnDemand"] = "on_demand";
	})(AnalysisTriggerRunPolicy || (lifecycle.AnalysisTriggerRunPolicy = AnalysisTriggerRunPolicy = {}));
	lifecycle.AnalysisTriggerRunPolicySchema = zod_1.default.nativeEnum(AnalysisTriggerRunPolicy);
	
	return lifecycle;
}

var triggers = {};

var dist$1 = {};

var Analysis = {};

var diffPair = {};

var diffPairNames = {};

var hasRequiredDiffPairNames;

function requireDiffPairNames () {
	if (hasRequiredDiffPairNames) return diffPairNames;
	hasRequiredDiffPairNames = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DIFF_PAIR_SUFFIX_PATTERNS = exports.DiffPairPolarity = void 0;
		exports.tryMatchDiffPairNames = tryMatchDiffPairNames;
		var DiffPairPolarity;
		(function (DiffPairPolarity) {
		    DiffPairPolarity["Positive"] = "positive";
		    DiffPairPolarity["Negative"] = "negative";
		})(DiffPairPolarity || (exports.DiffPairPolarity = DiffPairPolarity = {}));
		exports.DIFF_PAIR_SUFFIX_PATTERNS = [
		    ['_pos', '_neg'],
		    ['_p', '_n'],
		    ['+', '-'],
		    ['pos', 'neg'],
		    ['p', 'n'],
		];
		function tryMatchDiffPairNames(name1, name2) {
		    const lower1 = name1.toLowerCase();
		    const lower2 = name2.toLowerCase();
		    for (const [posSuffix, negSuffix] of exports.DIFF_PAIR_SUFFIX_PATTERNS) {
		        if (lower1.endsWith(posSuffix) && lower2.endsWith(negSuffix)) {
		            const base1 = lower1.slice(0, -posSuffix.length);
		            const base2 = lower2.slice(0, -negSuffix.length);
		            if (base1.length > 0 && base1 === base2) {
		                return { base: base1, polarity1: DiffPairPolarity.Positive, polarity2: DiffPairPolarity.Negative };
		            }
		        }
		        if (lower1.endsWith(negSuffix) && lower2.endsWith(posSuffix)) {
		            const base1 = lower1.slice(0, -negSuffix.length);
		            const base2 = lower2.slice(0, -posSuffix.length);
		            if (base1.length > 0 && base1 === base2) {
		                return { base: base1, polarity1: DiffPairPolarity.Negative, polarity2: DiffPairPolarity.Positive };
		            }
		        }
		    }
		    return null;
		}
		
	} (diffPairNames));
	return diffPairNames;
}

var hasRequiredDiffPair;

function requireDiffPair () {
	if (hasRequiredDiffPair) return diffPair;
	hasRequiredDiffPair = 1;
	(function (exports) {
		var __createBinding = (diffPair && diffPair.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (diffPair && diffPair.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireDiffPairNames(), exports);
		
	} (diffPair));
	return diffPair;
}

var hasRequiredAnalysis$1;

function requireAnalysis$1 () {
	if (hasRequiredAnalysis$1) return Analysis;
	hasRequiredAnalysis$1 = 1;
	(function (exports) {
		var __createBinding = (Analysis && Analysis.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Analysis && Analysis.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireDiffPair(), exports);
		
	} (Analysis));
	return Analysis;
}

var Common$2 = {};

var IPoint = {};

var hasRequiredIPoint;

function requireIPoint () {
	if (hasRequiredIPoint) return IPoint;
	hasRequiredIPoint = 1;
	Object.defineProperty(IPoint, "__esModule", { value: true });
	
	return IPoint;
}

var IRectangle = {};

var hasRequiredIRectangle;

function requireIRectangle () {
	if (hasRequiredIRectangle) return IRectangle;
	hasRequiredIRectangle = 1;
	Object.defineProperty(IRectangle, "__esModule", { value: true });
	IRectangle.getRectangleLength = getRectangleLength;
	function getRectangleLength(rect) {
	    return Math.sqrt(Math.pow(rect.x2 - rect.x1, 2) + Math.pow(rect.y2 - rect.y1, 2));
	}
	
	return IRectangle;
}

var hasRequiredCommon$3;

function requireCommon$3 () {
	if (hasRequiredCommon$3) return Common$2;
	hasRequiredCommon$3 = 1;
	(function (exports) {
		var __createBinding = (Common$2 && Common$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Common$2 && Common$2.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireIPoint(), exports);
		__exportStar(requireIRectangle(), exports);
		
	} (Common$2));
	return Common$2;
}

var DesignModels = {};

var SPICE = {};

var ISubModelSignalMapItemBase = {};

var SignalId = {};

var hasRequiredSignalId;

function requireSignalId () {
	if (hasRequiredSignalId) return SignalId;
	hasRequiredSignalId = 1;
	Object.defineProperty(SignalId, "__esModule", { value: true });
	SignalId.SimplifiedSignalIdSchema = SignalId.SignalIdSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	SignalId.SignalIdSchema = zod_1.default.union([zod_1.default.string(), zod_1.default.number()]);
	SignalId.SimplifiedSignalIdSchema = zod_1.default.coerce.string();
	
	return SignalId;
}

var hasRequiredISubModelSignalMapItemBase;

function requireISubModelSignalMapItemBase () {
	if (hasRequiredISubModelSignalMapItemBase) return ISubModelSignalMapItemBase;
	hasRequiredISubModelSignalMapItemBase = 1;
	Object.defineProperty(ISubModelSignalMapItemBase, "__esModule", { value: true });
	ISubModelSignalMapItemBase.ISubModelSignalMapItemBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SignalId_1 = requireSignalId();
	ISubModelSignalMapItemBase.ISubModelSignalMapItemBaseSchema = zod_1.z.object({
	    componentSignalId: SignalId_1.SignalIdSchema.optional(),
	    comment: zod_1.z.string().optional(),
	});
	
	return ISubModelSignalMapItemBase;
}

var ComponentSpiceModelDefinition = {};

var ComponentModelParameterDefinition = {};

var BoundaryClass = {};

var hasRequiredBoundaryClass;

function requireBoundaryClass () {
	if (hasRequiredBoundaryClass) return BoundaryClass;
	hasRequiredBoundaryClass = 1;
	Object.defineProperty(BoundaryClass, "__esModule", { value: true });
	BoundaryClass.BoundaryClassSchema = BoundaryClass.BoundaryClass = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryClass$1;
	(function (BoundaryClass) {
	    BoundaryClass["IC_AbsoluteMaximumRating"] = "ic.amr";
	    BoundaryClass["IC_ElectricalCharacteristics"] = "ic.ec";
	    BoundaryClass["IC_ElectricalCharacteristics_MeasurementPoint"] = "ic.ec.meas";
	    BoundaryClass["IC_TTL"] = "ic.ttl";
	    BoundaryClass["IC_TTL_INPUT"] = "ic.ttl.i";
	    BoundaryClass["IC_TTL_INPUT_HIGH"] = "ic.ttl.ih";
	    BoundaryClass["IC_TTL_INPUT_LOW"] = "ic.ttl.il";
	    BoundaryClass["IC_TTL_OUTPUT"] = "ic.ttl.o";
	    BoundaryClass["IC_TTL_OUTPUT_HIGH"] = "ic.ttl.oh";
	    BoundaryClass["IC_TTL_OUTPUT_LOW"] = "ic.ttl.ol";
	    BoundaryClass["MODE_SUPPLY_ON"] = "mode.supply.on";
	    BoundaryClass["MODE_ENABLE_ON"] = "mode.enable.on";
	})(BoundaryClass$1 || (BoundaryClass.BoundaryClass = BoundaryClass$1 = {}));
	BoundaryClass.BoundaryClassSchema = zod_1.z.nativeEnum(BoundaryClass$1);
	
	return BoundaryClass;
}

var hasRequiredComponentModelParameterDefinition;

function requireComponentModelParameterDefinition () {
	if (hasRequiredComponentModelParameterDefinition) return ComponentModelParameterDefinition;
	hasRequiredComponentModelParameterDefinition = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentModelParameterDefinitionSchema = exports.ComponentModelParameterDefinitionMapSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryClass_1 = requireBoundaryClass();
		exports.ComponentModelParameterDefinitionMapSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.lazy(() => exports.ComponentModelParameterDefinitionSchema));
		exports.ComponentModelParameterDefinitionSchema = zod_1.z.object({
		    name: zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.symbol()]),
		    description: zod_1.z.string().optional(),
		    classes: BoundaryClass_1.BoundaryClassSchema.array().optional(),
		    otherNames: zod_1.z.string().array().optional(),
		    type: zod_1.z.enum(['string', 'number', 'object']),
		    required: zod_1.z.boolean(),
		    isValueParameter: zod_1.z.boolean().optional(),
		    defaultValue: zod_1.z.any().optional(),
		    defaultVariance: zod_1.z.string().optional(),
		    units: zod_1.z
		        .object({
		        short: zod_1.z.string(),
		        long: zod_1.z.string(),
		        alternateNames: zod_1.z.string().array().optional(),
		        usesSIPrefixes: zod_1.z.boolean().optional(),
		    })
		        .optional(),
		});
		
	} (ComponentModelParameterDefinition));
	return ComponentModelParameterDefinition;
}

var SpiceModelTests = {};

var SpiceSourceOpts = {};

var hasRequiredSpiceSourceOpts;

function requireSpiceSourceOpts () {
	if (hasRequiredSpiceSourceOpts) return SpiceSourceOpts;
	hasRequiredSpiceSourceOpts = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISpiceSourceOptsSchema = exports.SpiceSourceTransientNoiseOptsSchema = exports.SpiceSourceSinusoidalDefinition = exports.SpiceSourceSinusoidalOptsSchema = exports.SpiceSourcePulseDefinition = exports.SpiceSourcePulseOptsSchema = exports.SpiceSourceExponentialDefinition = exports.SpiceSourceExponentialOptsSchema = exports.SpiceSourceDCDefinition = exports.SpiceSourceDCOptsSchema = exports.ISpiceSourceDefinitionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ComponentModelParameterDefinition_1 = requireComponentModelParameterDefinition();
		exports.ISpiceSourceDefinitionSchema = zod_1.z.object({
		    type: zod_1.z.string(),
		    sourceUnitParameters: zod_1.z.array(zod_1.z.string()),
		    parameters: ComponentModelParameterDefinition_1.ComponentModelParameterDefinitionMapSchema,
		});
		exports.SpiceSourceDCOptsSchema = zod_1.z.object({
		    sourceType: zod_1.z.literal('DC'),
		    dcValue: zod_1.z.number(),
		});
		exports.SpiceSourceDCDefinition = {
		    type: 'DC',
		    sourceUnitParameters: ['dcValue'],
		    parameters: {
		        dcValue: {
		            name: 'dcValue',
		            type: 'number',
		            required: true,
		        },
		    },
		};
		exports.SpiceSourceExponentialOptsSchema = zod_1.z.object({
		    sourceType: zod_1.z.literal('EXP'),
		    V1: zod_1.z.number(),
		    V2: zod_1.z.number(),
		    TD1: zod_1.z.number().optional(),
		    TAU1: zod_1.z.number().optional(),
		    TD2: zod_1.z.number().optional(),
		    TAU2: zod_1.z.number().optional(),
		    TSTEP: zod_1.z.number().optional(),
		});
		exports.SpiceSourceExponentialDefinition = {
		    type: 'EXP',
		    sourceUnitParameters: ['V1', 'V2'],
		    parameters: {
		        V1: {
		            name: 'V1',
		            type: 'number',
		            required: true,
		            description: 'Voltage or current value at time t=0, representing the initial value of the waveform before any exponential change occurs.',
		        },
		        V2: {
		            name: 'V2',
		            type: 'number',
		            required: true,
		            description: 'Voltage or current value during pulse, representing the value of the waveform after the exponential rise or fall.',
		        },
		        TD1: {
		            name: 'TD1',
		            type: 'number',
		            required: false,
		            description: 'Delay time before rising edge of pulse, the time interval before the exponential rise begins.',
		        },
		        TAU1: {
		            name: 'TAU1',
		            type: 'number',
		            required: false,
		            description: 'Rise time constant, the time constant for the exponential rise, determining how quickly the waveform rises to its peak value.',
		        },
		        TD2: {
		            name: 'TD2',
		            type: 'number',
		            required: false,
		            description: 'Delay time before falling edge of pulse, the time interval before the exponential fall begins.',
		        },
		        TAU2: {
		            name: 'TAU2',
		            type: 'number',
		            required: false,
		            description: 'Fall time constant, the time constant for the exponential fall, determining how quickly the waveform falls back to its initial value.',
		        },
		        TSTEP: {
		            name: 'TSTEP',
		            type: 'number',
		            required: false,
		            description: 'Time step, the interval between each step in the simulation of the exponential waveform.',
		        },
		    },
		};
		exports.SpiceSourcePulseOptsSchema = zod_1.z.object({
		    sourceType: zod_1.z.literal('PULSE'),
		    InitialValue: zod_1.z.number(),
		    PulsedValue: zod_1.z.number(),
		    DelayTime: zod_1.z.number(),
		    RiseTime: zod_1.z.number().optional(),
		    FallTime: zod_1.z.number().optional(),
		    PulseWidth: zod_1.z.number(),
		    Period: zod_1.z.number().optional(),
		    Phase: zod_1.z.number().optional(),
		});
		exports.SpiceSourcePulseDefinition = {
		    type: 'PULSE',
		    sourceUnitParameters: ['InitialValue', 'PulsedValue'],
		    parameters: {
		        InitialValue: {
		            name: 'InitialValue',
		            type: 'number',
		            required: true,
		            description: 'Initial value of the pulse, the starting voltage or current before the pulse begins.',
		        },
		        PulsedValue: {
		            name: 'PulsedValue',
		            type: 'number',
		            required: true,
		            description: 'Pulsed value of the pulse, the voltage or current value during the pulse.',
		        },
		        DelayTime: {
		            name: 'DelayTime',
		            type: 'number',
		            required: true,
		            description: 'Delay time before the pulse starts, the time interval before the pulse waveform begins.',
		        },
		        RiseTime: {
		            name: 'RiseTime',
		            type: 'number',
		            required: false,
		            description: 'Rise time of the pulse, the time taken for the pulse to rise from the initial value to the pulsed value.',
		        },
		        FallTime: {
		            name: 'FallTime',
		            type: 'number',
		            required: false,
		            description: 'Fall time of the pulse, the time taken for the pulse to fall from the pulsed value back to the initial value.',
		        },
		        PulseWidth: {
		            name: 'PulseWidth',
		            type: 'number',
		            required: true,
		            description: 'Width of the pulse, the duration for which the pulse remains at the pulsed value.',
		        },
		        Period: {
		            name: 'Period',
		            type: 'number',
		            required: false,
		            description: 'Period of the pulse, the total time for one complete cycle of the pulse waveform.',
		        },
		        Phase: {
		            name: 'Phase',
		            type: 'number',
		            required: false,
		            description: 'Phase of the pulse, the initial phase angle of the pulse waveform.',
		        },
		    },
		};
		exports.SpiceSourceSinusoidalOptsSchema = zod_1.z.object({
		    sourceType: zod_1.z.literal('SIN'),
		    VO: zod_1.z.number(),
		    VA: zod_1.z.number(),
		    FREQ: zod_1.z.number(),
		    THETA: zod_1.z.number().optional(),
		    TD: zod_1.z.number().optional(),
		    DAMP: zod_1.z.number().optional(),
		    PHASE: zod_1.z.number().optional(),
		});
		exports.SpiceSourceSinusoidalDefinition = {
		    type: 'SIN',
		    sourceUnitParameters: ['VO', 'VA'],
		    parameters: {
		        VO: {
		            name: 'VO',
		            type: 'number',
		            required: true,
		            description: 'DC offset, the constant voltage or current added to the sinusoidal waveform.',
		        },
		        VA: {
		            name: 'VA',
		            type: 'number',
		            required: true,
		            description: 'Amplitude, the peak value of the sinusoidal waveform.',
		        },
		        FREQ: {
		            name: 'FREQ',
		            type: 'number',
		            required: true,
		            description: 'Frequency, the number of cycles per second of the sinusoidal waveform.',
		        },
		        THETA: {
		            name: 'THETA',
		            type: 'number',
		            required: false,
		            description: 'Phase shift, the initial angle of the sinusoidal waveform in degrees.',
		        },
		        TD: {
		            name: 'TD',
		            type: 'number',
		            required: false,
		            description: 'Time delay, the time before the sinusoidal waveform starts.',
		        },
		        DAMP: {
		            name: 'DAMP',
		            type: 'number',
		            required: false,
		            description: 'Damping factor, the rate at which the amplitude of the sinusoidal waveform decreases over time.',
		        },
		        PHASE: {
		            name: 'PHASE',
		            type: 'number',
		            required: false,
		            description: 'Phase, another term for phase shift, specifying the initial angle of the sinusoidal waveform in degrees.',
		        },
		    },
		};
		exports.SpiceSourceTransientNoiseOptsSchema = zod_1.z.object({
		    sourceType: zod_1.z.literal('TRANNOISE'),
		    NA: zod_1.z.number().optional(),
		    NT: zod_1.z.number().optional(),
		    NALPHA: zod_1.z.number().optional(),
		    NAMP: zod_1.z.number().optional(),
		    RTSAM: zod_1.z.number().optional(),
		    RTSCAPT: zod_1.z.number().optional(),
		    RTSEMT: zod_1.z.number().optional(),
		});
		exports.ISpiceSourceOptsSchema = zod_1.z.discriminatedUnion('sourceType', [
		    exports.SpiceSourceDCOptsSchema,
		    exports.SpiceSourceExponentialOptsSchema,
		    exports.SpiceSourcePulseOptsSchema,
		    exports.SpiceSourceSinusoidalOptsSchema,
		    exports.SpiceSourceTransientNoiseOptsSchema,
		]);
		
	} (SpiceSourceOpts));
	return SpiceSourceOpts;
}

var hasRequiredSpiceModelTests;

function requireSpiceModelTests () {
	if (hasRequiredSpiceModelTests) return SpiceModelTests;
	hasRequiredSpiceModelTests = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISpiceModelSimulationTestSchema = exports.ISpiceModelSimulationControlledVariableSchema = exports.ISpiceModelSimulationVariableTemperatureSchema = exports.ISpiceModelSimulationRespondingVariableSchema = exports.ISpiceModelSimulationVariableDefinitionSchema = exports.ISpiceModelSimulationVariableBaseSchema = exports.ISpiceModelSimulationVariableSourceFunctionSchema = exports.ISpiceModelSimulationVariableSeriesValueSchemaT = exports.ISpiceModelSimulationVariableSeriesValueSchema = exports.ISpiceModelTestSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SpiceSourceOpts_1 = requireSpiceSourceOpts();
		exports.ISpiceModelTestSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    name: zod_1.z.string(),
		    description: zod_1.z.string().optional(),
		    sourceReferenceMetadata: zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional(),
		});
		exports.ISpiceModelSimulationVariableSeriesValueSchema = zod_1.z.object({
		    type: zod_1.z.literal('series'),
		    units: zod_1.z.enum(['voltage', 'current', 'power', 'frequency', 'phase']),
		    steps: zod_1.z.array(zod_1.z.object({ step: zod_1.z.number(), value: zod_1.z.number() })),
		});
		const ISpiceModelSimulationVariableSeriesValueSchemaT = (tValue) => zod_1.z.object({
		    type: zod_1.z.literal('series'),
		    units: tValue,
		    steps: zod_1.z.array(zod_1.z.object({ step: zod_1.z.number(), value: zod_1.z.number() })),
		});
		exports.ISpiceModelSimulationVariableSeriesValueSchemaT = ISpiceModelSimulationVariableSeriesValueSchemaT;
		exports.ISpiceModelSimulationVariableSourceFunctionSchema = zod_1.z.object({
		    type: zod_1.z.literal('source-function'),
		    units: zod_1.z.enum(['voltage', 'current']),
		    opts: SpiceSourceOpts_1.ISpiceSourceOptsSchema,
		});
		exports.ISpiceModelSimulationVariableBaseSchema = zod_1.z.object({
		    type: zod_1.z.enum(['controlled', 'responding', 'temperature']),
		    variable: zod_1.z.string(),
		    value: exports.ISpiceModelSimulationVariableSeriesValueSchema,
		});
		exports.ISpiceModelSimulationVariableDefinitionSchema = exports.ISpiceModelSimulationVariableBaseSchema.extend({
		    description: zod_1.z.string().optional(),
		});
		exports.ISpiceModelSimulationRespondingVariableSchema = exports.ISpiceModelSimulationVariableDefinitionSchema.extend({
		    type: zod_1.z.literal('responding'),
		});
		exports.ISpiceModelSimulationVariableTemperatureSchema = zod_1.z.object({
		    type: zod_1.z.literal('temperature'),
		    value: zod_1.z.number(),
		});
		exports.ISpiceModelSimulationControlledVariableSchema = exports.ISpiceModelSimulationVariableDefinitionSchema.extend({
		    type: zod_1.z.literal('controlled'),
		    valueType: zod_1.z.enum(['voltage', 'current']),
		    value: zod_1.z.union([
		        (0, exports.ISpiceModelSimulationVariableSeriesValueSchemaT)(zod_1.z.enum(['voltage', 'current'])),
		        exports.ISpiceModelSimulationVariableTemperatureSchema,
		        exports.ISpiceModelSimulationVariableSourceFunctionSchema,
		    ]),
		});
		exports.ISpiceModelSimulationTestSchema = exports.ISpiceModelTestSchema.extend({
		    type: zod_1.z.literal('simulation'),
		    harness: zod_1.z.string().optional(),
		    variables: zod_1.z.array(zod_1.z.union([exports.ISpiceModelSimulationRespondingVariableSchema, exports.ISpiceModelSimulationControlledVariableSchema])),
		});
		
	} (SpiceModelTests));
	return SpiceModelTests;
}

var IModelInfoBase = {};

var hasRequiredIModelInfoBase;

function requireIModelInfoBase () {
	if (hasRequiredIModelInfoBase) return IModelInfoBase;
	hasRequiredIModelInfoBase = 1;
	Object.defineProperty(IModelInfoBase, "__esModule", { value: true });
	IModelInfoBase.IModelInfoBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IModelInfoBaseSchema = (tSignalMapSchema) => zod_1.z.object({
	    signalConnectionMap: tSignalMapSchema
	        .array()
	        .describe('The map of sub model signals to component signals, modes, and internal nets.'),
	    parameterMap: zod_1.z.any().optional().describe('The map of sub model parameters to component values'),
	    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional().describe('Arbitrary optional metadata describing this model'),
	});
	IModelInfoBase.IModelInfoBaseSchema = IModelInfoBaseSchema;
	
	return IModelInfoBase;
}

var hasRequiredComponentSpiceModelDefinition;

function requireComponentSpiceModelDefinition () {
	if (hasRequiredComponentSpiceModelDefinition) return ComponentSpiceModelDefinition;
	hasRequiredComponentSpiceModelDefinition = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IComponentSpiceModelDefinitionSchema = exports.IComponentSpiceModelDefinitionModelSchema = exports.IComponentSpiceModelDefinitionSubcircuitSchema = exports.IComponentSigmaModelSignalMapItemSchema = exports.IComponentSpiceModelSignalMapItemSchema = exports.IComponentSpiceModelDefinitionBaseSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ComponentModelParameterDefinition_1 = requireComponentModelParameterDefinition();
		const SpiceModelTests_1 = requireSpiceModelTests();
		const ISubModelSignalMapItemBase_1 = requireISubModelSignalMapItemBase();
		const IModelInfoBase_1 = requireIModelInfoBase();
		const SignalId_1 = requireSignalId();
		exports.IComponentSpiceModelDefinitionBaseSchema = zod_1.z.object({
		    modelName: zod_1.z.string(),
		    modelPath: zod_1.z.string().optional(),
		    modelType: zod_1.z.enum(['subcircuit', 'model']),
		    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional(),
		});
		exports.IComponentSpiceModelSignalMapItemSchema = ISubModelSignalMapItemBase_1.ISubModelSignalMapItemBaseSchema.extend({
		    modelSignalId: SignalId_1.SignalIdSchema.optional(),
		    modeId: zod_1.z.string().optional(),
		    internalNet: zod_1.z.string().optional(),
		    paramNo: zod_1.z.number(),
		});
		exports.IComponentSigmaModelSignalMapItemSchema = ISubModelSignalMapItemBase_1.ISubModelSignalMapItemBaseSchema.extend({
		    modelSignalId: SignalId_1.SignalIdSchema,
		    componentSignalId: SignalId_1.SignalIdSchema,
		});
		exports.IComponentSpiceModelDefinitionSubcircuitSchema = exports.IComponentSpiceModelDefinitionBaseSchema.merge((0, IModelInfoBase_1.IModelInfoBaseSchema)(exports.IComponentSpiceModelSignalMapItemSchema)).extend({
		    modelType: zod_1.z.literal('subcircuit'),
		    parameters: ComponentModelParameterDefinition_1.ComponentModelParameterDefinitionSchema.array().optional(),
		    subcircuitName: zod_1.z.string().optional(),
		    tests: SpiceModelTests_1.ISpiceModelTestSchema.array().optional(),
		});
		exports.IComponentSpiceModelDefinitionModelSchema = exports.IComponentSpiceModelDefinitionBaseSchema.extend({
		    modelName: zod_1.z.string(),
		    model: zod_1.z.string(),
		    modelType: zod_1.z.literal('model'),
		});
		exports.IComponentSpiceModelDefinitionSchema = zod_1.z.discriminatedUnion('modelType', [
		    exports.IComponentSpiceModelDefinitionSubcircuitSchema,
		    exports.IComponentSpiceModelDefinitionModelSchema,
		]);
		
	} (ComponentSpiceModelDefinition));
	return ComponentSpiceModelDefinition;
}

var ISimulationManifestEntry = {};

var INetStats = {};

var BoundaryCharacteristicStateSummaryExternal = {};

var BoundaryAssertionStrength = {};

var hasRequiredBoundaryAssertionStrength;

function requireBoundaryAssertionStrength () {
	if (hasRequiredBoundaryAssertionStrength) return BoundaryAssertionStrength;
	hasRequiredBoundaryAssertionStrength = 1;
	Object.defineProperty(BoundaryAssertionStrength, "__esModule", { value: true });
	BoundaryAssertionStrength.BoundaryAssertionStrengthSchema = BoundaryAssertionStrength.BoundaryAssertionStrength = void 0;
	BoundaryAssertionStrength.getHighestAssertionStrength = getHighestAssertionStrength;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryAssertionStrength$1;
	(function (BoundaryAssertionStrength) {
	    BoundaryAssertionStrength["None"] = "none";
	    BoundaryAssertionStrength["Implication"] = "implication";
	    BoundaryAssertionStrength["Assertion"] = "assertion";
	})(BoundaryAssertionStrength$1 || (BoundaryAssertionStrength.BoundaryAssertionStrength = BoundaryAssertionStrength$1 = {}));
	BoundaryAssertionStrength.BoundaryAssertionStrengthSchema = zod_1.z.nativeEnum(BoundaryAssertionStrength$1);
	function getHighestAssertionStrength(a, b) {
	    return (a === BoundaryAssertionStrength$1.Assertion || b === BoundaryAssertionStrength$1.Assertion ?
	        BoundaryAssertionStrength$1.Assertion
	        : a === BoundaryAssertionStrength$1.Implication || b === BoundaryAssertionStrength$1.Implication ?
	            BoundaryAssertionStrength$1.Implication
	            : BoundaryAssertionStrength$1.None);
	}
	
	return BoundaryAssertionStrength;
}

var BoundaryCharacteristic = {};

var hasRequiredBoundaryCharacteristic;

function requireBoundaryCharacteristic () {
	if (hasRequiredBoundaryCharacteristic) return BoundaryCharacteristic;
	hasRequiredBoundaryCharacteristic = 1;
	Object.defineProperty(BoundaryCharacteristic, "__esModule", { value: true });
	BoundaryCharacteristic.BoundaryCharacteristicSIDefinitions = BoundaryCharacteristic.BoundaryCharacteristicRelativeFieldPolicyMap = BoundaryCharacteristic.BoundaryFieldValidationPolicy = BoundaryCharacteristic.RatedBoundaryCharacteristics = BoundaryCharacteristic.BoundaryCharacteristicSchema = BoundaryCharacteristic.BoundaryCharacteristic = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryCharacteristic$1;
	(function (BoundaryCharacteristic) {
	    BoundaryCharacteristic["Voltage"] = "voltage";
	    BoundaryCharacteristic["Current"] = "current";
	    BoundaryCharacteristic["Temperature"] = "temperature";
	    BoundaryCharacteristic["Power"] = "power";
	    BoundaryCharacteristic["Frequency"] = "frequency";
	    BoundaryCharacteristic["Inductance"] = "inductance";
	    BoundaryCharacteristic["Resistance"] = "resistance";
	    BoundaryCharacteristic["Capacitance"] = "capacitance";
	    BoundaryCharacteristic["TemperatureKelvin"] = "temperatureKelvin";
	})(BoundaryCharacteristic$1 || (BoundaryCharacteristic.BoundaryCharacteristic = BoundaryCharacteristic$1 = {}));
	BoundaryCharacteristic.BoundaryCharacteristicSchema = zod_1.z.nativeEnum(BoundaryCharacteristic$1);
	BoundaryCharacteristic.RatedBoundaryCharacteristics = [
	    BoundaryCharacteristic$1.Voltage,
	    BoundaryCharacteristic$1.Current,
	    BoundaryCharacteristic$1.Power,
	];
	var BoundaryFieldValidationPolicy;
	(function (BoundaryFieldValidationPolicy) {
	    BoundaryFieldValidationPolicy["Required"] = "required";
	    BoundaryFieldValidationPolicy["Recommended"] = "recommended";
	    BoundaryFieldValidationPolicy["Optional"] = "optional";
	    BoundaryFieldValidationPolicy["Unexpected"] = "unexpected";
	})(BoundaryFieldValidationPolicy || (BoundaryCharacteristic.BoundaryFieldValidationPolicy = BoundaryFieldValidationPolicy = {}));
	BoundaryCharacteristic.BoundaryCharacteristicRelativeFieldPolicyMap = {
	    [BoundaryCharacteristic$1.Voltage]: BoundaryFieldValidationPolicy.Recommended,
	    [BoundaryCharacteristic$1.Power]: BoundaryFieldValidationPolicy.Required,
	    [BoundaryCharacteristic$1.Current]: BoundaryFieldValidationPolicy.Unexpected,
	    [BoundaryCharacteristic$1.Temperature]: BoundaryFieldValidationPolicy.Optional,
	    [BoundaryCharacteristic$1.TemperatureKelvin]: BoundaryFieldValidationPolicy.Optional,
	    [BoundaryCharacteristic$1.Frequency]: BoundaryFieldValidationPolicy.Unexpected,
	    [BoundaryCharacteristic$1.Inductance]: BoundaryFieldValidationPolicy.Unexpected,
	    [BoundaryCharacteristic$1.Resistance]: BoundaryFieldValidationPolicy.Unexpected,
	    [BoundaryCharacteristic$1.Capacitance]: BoundaryFieldValidationPolicy.Unexpected,
	};
	BoundaryCharacteristic.BoundaryCharacteristicSIDefinitions = {
	    [BoundaryCharacteristic$1.Voltage]: {
	        long: 'volts',
	        short: 'V',
	        alternateNames: ['volt'],
	        usesSIPrefixes: true,
	        epsilon: 1e-9,
	    },
	    [BoundaryCharacteristic$1.Current]: {
	        long: 'amps',
	        short: 'A',
	        alternateNames: ['amperes', 'amp'],
	        usesSIPrefixes: true,
	        epsilon: 1e-9,
	    },
	    [BoundaryCharacteristic$1.Temperature]: {
	        long: 'celsius',
	        short: 'C',
	        alternateNames: ['℃', 'ºc', 'ºC', 'º C', 'º c'],
	        usesSIPrefixes: true,
	        epsilon: 1e-6,
	    },
	    [BoundaryCharacteristic$1.TemperatureKelvin]: {
	        long: 'kelvin',
	        short: 'K',
	        alternateNames: ['ºk', 'ºK', 'º K', 'º k'],
	        usesSIPrefixes: true,
	        epsilon: 1e-6,
	    },
	    [BoundaryCharacteristic$1.Power]: {
	        long: 'watts',
	        short: 'W',
	        alternateNames: ['watt'],
	        usesSIPrefixes: true,
	        epsilon: 1e-9,
	    },
	    [BoundaryCharacteristic$1.Frequency]: {
	        long: 'hertz',
	        short: 'Hz',
	        usesSIPrefixes: true,
	        epsilon: 1e-6,
	    },
	    [BoundaryCharacteristic$1.Inductance]: {
	        long: 'henry',
	        short: 'H',
	        usesSIPrefixes: true,
	        epsilon: 1e-9,
	    },
	    [BoundaryCharacteristic$1.Capacitance]: {
	        long: 'farad',
	        short: 'F',
	        usesSIPrefixes: true,
	        epsilon: 1e-12,
	    },
	    [BoundaryCharacteristic$1.Resistance]: {
	        long: 'ohm',
	        short: 'Ω',
	        usesSIPrefixes: true,
	        epsilon: 1e-6,
	    },
	};
	
	return BoundaryCharacteristic;
}

var CharacteristicValue = {};

var AbsoluteCharacteristicValueBase = {};

var ISerializedProvenanceNode = {};

var hasRequiredISerializedProvenanceNode;

function requireISerializedProvenanceNode () {
	if (hasRequiredISerializedProvenanceNode) return ISerializedProvenanceNode;
	hasRequiredISerializedProvenanceNode = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISerializedCharacteristicProvenanceSchema = exports.ISerializedProvenanceNodeSchema = exports.ISerializedRelativeResolutionSchema = exports.ISerializedValueContributionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
		const SignalId_1 = requireSignalId();
		const nanSafeNumber = zod_1.default
		    .union([zod_1.default.number(), zod_1.default.nan(), zod_1.default.null()])
		    .optional()
		    .nullish()
		    .transform((v) => (v === null || v === undefined || Number.isNaN(v) ? undefined : v));
		exports.ISerializedValueContributionSchema = zod_1.default.object({
		    value: nanSafeNumber,
		    allowableRange: zod_1.default
		        .object({
		        min: nanSafeNumber,
		        max: nanSafeNumber,
		    })
		        .optional(),
		});
		exports.ISerializedRelativeResolutionSchema = zod_1.default.lazy(() => zod_1.default.object({
		    net: zod_1.default.string(),
		    characteristic: BoundaryCharacteristic_1.BoundaryCharacteristicSchema,
		    resolvedValue: exports.ISerializedValueContributionSchema.optional(),
		    provenance: zod_1.default.array(exports.ISerializedProvenanceNodeSchema).optional(),
		}));
		exports.ISerializedProvenanceNodeSchema = zod_1.default.lazy(() => zod_1.default.object({
		    boundaryId: zod_1.default.string(),
		    fullModuleName: zod_1.default.string(),
		    signalId: SignalId_1.SignalIdSchema.optional(),
		    contributed: exports.ISerializedValueContributionSchema,
		    relativeTo: exports.ISerializedRelativeResolutionSchema.optional(),
		}));
		exports.ISerializedCharacteristicProvenanceSchema = zod_1.default.lazy(() => zod_1.default.object({
		    contributions: zod_1.default.array(exports.ISerializedProvenanceNodeSchema).optional(),
		    limitingFactor: zod_1.default
		        .object({
		        min: zod_1.default.union([exports.ISerializedProvenanceNodeSchema, zod_1.default.array(exports.ISerializedProvenanceNodeSchema)]).optional(),
		        max: zod_1.default.union([exports.ISerializedProvenanceNodeSchema, zod_1.default.array(exports.ISerializedProvenanceNodeSchema)]).optional(),
		    })
		        .optional(),
		}));
		
	} (ISerializedProvenanceNode));
	return ISerializedProvenanceNode;
}

var hasRequiredAbsoluteCharacteristicValueBase;

function requireAbsoluteCharacteristicValueBase () {
	if (hasRequiredAbsoluteCharacteristicValueBase) return AbsoluteCharacteristicValueBase;
	hasRequiredAbsoluteCharacteristicValueBase = 1;
	Object.defineProperty(AbsoluteCharacteristicValueBase, "__esModule", { value: true });
	AbsoluteCharacteristicValueBase.AbsoluteCharacteristicValueBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ISerializedProvenanceNode_1 = requireISerializedProvenanceNode();
	AbsoluteCharacteristicValueBase.AbsoluteCharacteristicValueBaseSchema = zod_1.z.object({
	    value: zod_1.z
	        .number()
	        .optional()
	        .nullish()
	        .transform((v) => v ?? undefined),
	    allowableRange: zod_1.z.object({
	        min: zod_1.z
	            .number()
	            .optional()
	            .nullish()
	            .transform((v) => v ?? undefined),
	        max: zod_1.z
	            .number()
	            .optional()
	            .nullish()
	            .transform((v) => v ?? undefined),
	    }),
	    discreteValues: zod_1.z
	        .object({
	        value: zod_1.z.number(),
	    })
	        .array()
	        .optional()
	        .nullish()
	        .transform((v) => v ?? undefined),
	    provenance: ISerializedProvenanceNode_1.ISerializedProvenanceNodeSchema.optional()
	        .nullish()
	        .transform((v) => v ?? undefined),
	});
	
	return AbsoluteCharacteristicValueBase;
}

var hasRequiredCharacteristicValue;

function requireCharacteristicValue () {
	if (hasRequiredCharacteristicValue) return CharacteristicValue;
	hasRequiredCharacteristicValue = 1;
	Object.defineProperty(CharacteristicValue, "__esModule", { value: true });
	CharacteristicValue.CharacteristicValueSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AbsoluteCharacteristicValueBase_1 = requireAbsoluteCharacteristicValueBase();
	CharacteristicValue.CharacteristicValueSchema = AbsoluteCharacteristicValueBase_1.AbsoluteCharacteristicValueBaseSchema.extend({
	    relativeToNet: zod_1.default.string().optional(),
	});
	
	return CharacteristicValue;
}

var hasRequiredBoundaryCharacteristicStateSummaryExternal;

function requireBoundaryCharacteristicStateSummaryExternal () {
	if (hasRequiredBoundaryCharacteristicStateSummaryExternal) return BoundaryCharacteristicStateSummaryExternal;
	hasRequiredBoundaryCharacteristicStateSummaryExternal = 1;
	Object.defineProperty(BoundaryCharacteristicStateSummaryExternal, "__esModule", { value: true });
	BoundaryCharacteristicStateSummaryExternal.BoundaryCharacteristicStateSummaryExternalSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryAssertionStrength_1 = requireBoundaryAssertionStrength();
	const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
	const DesignModels_1 = requireDesignModels();
	const CharacteristicValue_1 = requireCharacteristicValue();
	const ISerializedProvenanceNode_1 = requireISerializedProvenanceNode();
	BoundaryCharacteristicStateSummaryExternal.BoundaryCharacteristicStateSummaryExternalSchema = zod_1.default.object({
	    assertionStrength: zod_1.default.lazy(() => BoundaryAssertionStrength_1.BoundaryAssertionStrengthSchema),
	    characteristic: zod_1.default.lazy(() => BoundaryCharacteristic_1.BoundaryCharacteristicSchema),
	    derivedFromBoundaries: zod_1.default.lazy(() => DesignModels_1.BoundaryGlobalIdRefSchema.array()),
	    derivedFromSimulations: zod_1.default
	        .lazy(() => DesignModels_1.SimulationDefinitionSchema)
	        .array()
	        .optional(),
	    derivedFromSpecifications: zod_1.default.string().array().optional(),
	    value: zod_1.default.lazy(() => CharacteristicValue_1.CharacteristicValueSchema),
	    isPartialResolution: zod_1.default.boolean().optional(),
	    fromSource: zod_1.default.boolean().optional(),
	    provenance: zod_1.default.lazy(() => ISerializedProvenanceNode_1.ISerializedCharacteristicProvenanceSchema).optional(),
	});
	
	return BoundaryCharacteristicStateSummaryExternal;
}

var BoundaryCharacteristicStateSerialized = {};

var AbsoluteCharacteristicValue = {};

var hasRequiredAbsoluteCharacteristicValue;

function requireAbsoluteCharacteristicValue () {
	if (hasRequiredAbsoluteCharacteristicValue) return AbsoluteCharacteristicValue;
	hasRequiredAbsoluteCharacteristicValue = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AbsoluteCharacteristicValueSchema = exports.IDiscreteValueSchema = exports.SimulationDefinitionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const DesignModels_1 = requireDesignModels();
		const AbsoluteCharacteristicValueBase_1 = requireAbsoluteCharacteristicValueBase();
		exports.SimulationDefinitionSchema = zod_1.default.object({
		    id: zod_1.default.string(),
		});
		exports.IDiscreteValueSchema = zod_1.default.object({
		    value: zod_1.default.number(),
		    derivedFrom: zod_1.default
		        .lazy(() => zod_1.default.union([DesignModels_1.BoundaryGlobalIdRefSchema, exports.SimulationDefinitionSchema]))
		        .array()
		        .optional(),
		});
		exports.AbsoluteCharacteristicValueSchema = AbsoluteCharacteristicValueBase_1.AbsoluteCharacteristicValueBaseSchema.extend({
		    discreteValues: exports.IDiscreteValueSchema.array().optional(),
		});
		
	} (AbsoluteCharacteristicValue));
	return AbsoluteCharacteristicValue;
}

var ISerializedCircuitState = {};

var ComponentFunctionStatesSerialized = {};

var ISerializedTraversal = {};

var FunctionalAddressing = {};

var IExternalComponent = {};

var BoundaryLevel = {};

var hasRequiredBoundaryLevel;

function requireBoundaryLevel () {
	if (hasRequiredBoundaryLevel) return BoundaryLevel;
	hasRequiredBoundaryLevel = 1;
	Object.defineProperty(BoundaryLevel, "__esModule", { value: true });
	BoundaryLevel.BoundaryLevelSchema = BoundaryLevel.BoundaryLevel = void 0;
	BoundaryLevel.getHighestBoundaryLevel = getHighestBoundaryLevel;
	BoundaryLevel.getHighestBoundaryLevelFromArray = getHighestBoundaryLevelFromArray;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryLevel$1;
	(function (BoundaryLevel) {
	    BoundaryLevel["Recommendation"] = "recommend";
	    BoundaryLevel["Warning"] = "warning";
	    BoundaryLevel["Error"] = "error";
	})(BoundaryLevel$1 || (BoundaryLevel.BoundaryLevel = BoundaryLevel$1 = {}));
	BoundaryLevel.BoundaryLevelSchema = zod_1.z.nativeEnum(BoundaryLevel$1);
	function getHighestBoundaryLevel(a, b) {
	    return (a === BoundaryLevel$1.Error || b === BoundaryLevel$1.Error ? BoundaryLevel$1.Error
	        : a === BoundaryLevel$1.Warning || b === BoundaryLevel$1.Warning ? BoundaryLevel$1.Warning
	            : BoundaryLevel$1.Recommendation);
	}
	function getHighestBoundaryLevelFromArray(levels, defaultLevel = BoundaryLevel$1.Recommendation) {
	    return (levels.reduce((acc, level) => (level !== undefined ? getHighestBoundaryLevel(acc, level) : acc), defaultLevel) ?? defaultLevel);
	}
	
	return BoundaryLevel;
}

var NumericalBoundary = {};

var hasRequiredNumericalBoundary;

function requireNumericalBoundary () {
	if (hasRequiredNumericalBoundary) return NumericalBoundary;
	hasRequiredNumericalBoundary = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NumericalBoundarySchema = exports.NumericalBoundaryAbsoluteSchema = exports.NumericalBoundaryRangeSchema = exports.NumericalBoundaryRangeModeSchema = exports.NumericalBoundaryMaximumSchema = exports.NumericalBoundaryMinimumSchema = exports.NumericalBoundaryBaseSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
		exports.NumericalBoundaryBaseSchema = zod_1.z.object({
		    type: zod_1.z.string(),
		    characteristic: BoundaryCharacteristic_1.BoundaryCharacteristicSchema,
		    nominalValue: zod_1.z.number().optional(),
		    variance: zod_1.z.string().optional(),
		    useAbsVal: zod_1.z.boolean().optional(),
		});
		exports.NumericalBoundaryMinimumSchema = exports.NumericalBoundaryBaseSchema.extend({
		    type: zod_1.z.literal('minimum'),
		    value: zod_1.z.number(),
		    valInclusive: zod_1.z.boolean().optional(),
		}).describe('A minimum value boundary describes the minimal value of the target characteristic with respect to the relative target.');
		exports.NumericalBoundaryMaximumSchema = exports.NumericalBoundaryBaseSchema.extend({
		    type: zod_1.z.literal('maximum'),
		    value: zod_1.z.number(),
		    valInclusive: zod_1.z.boolean().optional(),
		}).describe('A maximum value boundary describes the maximal value of the target characteristic with respect to the relative target.');
		exports.NumericalBoundaryRangeModeSchema = zod_1.z.enum(['inclusive', 'exclusive']);
		exports.NumericalBoundaryRangeSchema = exports.NumericalBoundaryBaseSchema.extend({
		    type: zod_1.z.literal('range'),
		    rangeMode: exports.NumericalBoundaryRangeModeSchema.optional(),
		    minVal: zod_1.z.number(),
		    minValIncluded: zod_1.z.boolean().optional(),
		    maxVal: zod_1.z.number(),
		    maxValIncluded: zod_1.z.boolean().optional(),
		}).describe('A range boundary describes the range of values that the target characteristic must stay within with respect to the relative target.');
		exports.NumericalBoundaryAbsoluteSchema = exports.NumericalBoundaryBaseSchema.extend({
		    type: zod_1.z.literal('absolute'),
		    value: zod_1.z.number(),
		}).describe('An absolute boundary describes the exact value that the target characteristic must take on with respect to the relative target.');
		exports.NumericalBoundarySchema = zod_1.z.discriminatedUnion('type', [
		    exports.NumericalBoundaryMinimumSchema,
		    exports.NumericalBoundaryMaximumSchema,
		    exports.NumericalBoundaryRangeSchema,
		    exports.NumericalBoundaryAbsoluteSchema,
		]);
		
	} (NumericalBoundary));
	return NumericalBoundary;
}

var FunctionReferenceCondition = {};

var hasRequiredFunctionReferenceCondition;

function requireFunctionReferenceCondition () {
	if (hasRequiredFunctionReferenceCondition) return FunctionReferenceCondition;
	hasRequiredFunctionReferenceCondition = 1;
	Object.defineProperty(FunctionReferenceCondition, "__esModule", { value: true });
	FunctionReferenceCondition.FunctionReferenceConditionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	FunctionReferenceCondition.FunctionReferenceConditionSchema = zod_1.z.object({
	    function: zod_1.z.string(),
	    function_state: zod_1.z.boolean(),
	});
	
	return FunctionReferenceCondition;
}

var TraversalHelpers = {};

var TraversalSolvableCharacteristic = {};

var hasRequiredTraversalSolvableCharacteristic;

function requireTraversalSolvableCharacteristic () {
	if (hasRequiredTraversalSolvableCharacteristic) return TraversalSolvableCharacteristic;
	hasRequiredTraversalSolvableCharacteristic = 1;
	Object.defineProperty(TraversalSolvableCharacteristic, "__esModule", { value: true });
	TraversalSolvableCharacteristic.TraversalSolvableCharacteristicSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
	TraversalSolvableCharacteristic.TraversalSolvableCharacteristicSchema = zod_1.z.union([
	    BoundaryCharacteristic_1.BoundaryCharacteristicSchema,
	    zod_1.z.literal('data'),
	]);
	
	return TraversalSolvableCharacteristic;
}

var hasRequiredTraversalHelpers;

function requireTraversalHelpers () {
	if (hasRequiredTraversalHelpers) return TraversalHelpers;
	hasRequiredTraversalHelpers = 1;
	Object.defineProperty(TraversalHelpers, "__esModule", { value: true });
	TraversalHelpers.TraversalSolvableCharacteristicMatcherSchema = void 0;
	TraversalHelpers.isTraversalType = isTraversalType;
	TraversalHelpers.isTraversalMatcher = isTraversalMatcher;
	TraversalHelpers.isFunctionReferenceCondition = isFunctionReferenceCondition;
	TraversalHelpers.isExternalComponentCondition = isExternalComponentCondition;
	const TraversalSolvableCharacteristic_1 = requireTraversalSolvableCharacteristic();
	const zod_1 = /*@__PURE__*/ requireZod();
	function isTraversalType(t, type) {
	    return typeof t === 'string' ? t === type : t.type === type;
	}
	function isTraversalMatcher(t) {
	    return (typeof t !== 'string' &&
	        typeof t === 'object' &&
	        !!t &&
	        'type' in t &&
	        ('endTraversalAtModuleStep' in t || 'endTraversalAtNetStep' in t));
	}
	TraversalHelpers.TraversalSolvableCharacteristicMatcherSchema = zod_1.default
	    .object({
	    type: TraversalSolvableCharacteristic_1.TraversalSolvableCharacteristicSchema,
	    endTraversalAtModuleStep: zod_1.default.function().output(zod_1.default.boolean()).optional(),
	    endTraversalAtNetStep: zod_1.default.function().output(zod_1.default.boolean()).optional(),
	})
	    .passthrough();
	function isFunctionReferenceCondition(condition) {
	    return condition.function !== undefined;
	}
	function isExternalComponentCondition(condition) {
	    return (condition.name !== undefined &&
	        Array.isArray(condition.components));
	}
	
	return TraversalHelpers;
}

var IExternalComponetConnection = {};

var IModeCondition = {};

var ModeBoundaryCondition = {};

var IBoundaryDefinition = {};

var IConnectionBoundaryDefinition = {};

var BoundaryAssertionType = {};

var hasRequiredBoundaryAssertionType;

function requireBoundaryAssertionType () {
	if (hasRequiredBoundaryAssertionType) return BoundaryAssertionType;
	hasRequiredBoundaryAssertionType = 1;
	Object.defineProperty(BoundaryAssertionType, "__esModule", { value: true });
	BoundaryAssertionType.BoundaryAssertionTypeSchema = BoundaryAssertionType.BoundaryAssertionType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryAssertionType$1;
	(function (BoundaryAssertionType) {
	    BoundaryAssertionType["Numerical"] = "numerical";
	    BoundaryAssertionType["Connection"] = "connection";
	})(BoundaryAssertionType$1 || (BoundaryAssertionType.BoundaryAssertionType = BoundaryAssertionType$1 = {}));
	BoundaryAssertionType.BoundaryAssertionTypeSchema = zod_1.z.nativeEnum(BoundaryAssertionType$1);
	
	return BoundaryAssertionType;
}

var RelativeSignal = {};

var hasRequiredRelativeSignal;

function requireRelativeSignal () {
	if (hasRequiredRelativeSignal) return RelativeSignal;
	hasRequiredRelativeSignal = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RelativeSignalSimplifiedSchema = exports.RelativeSignalSchema = exports.RelativeSignalNameTypeSimplifiedSchema = exports.RelativeSignalNameTypeSchema = exports.RelativeSignalSignalTypeSimplifiedSchema = exports.RelativeSignalSignalTypeSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SignalId_1 = requireSignalId();
		exports.RelativeSignalSignalTypeSchema = zod_1.z.union([SignalId_1.SignalIdSchema, SignalId_1.SignalIdSchema.array()]);
		exports.RelativeSignalSignalTypeSimplifiedSchema = zod_1.z.preprocess((val) => {
		    if (Array.isArray(val)) {
		        return val;
		    }
		    return [val];
		}, SignalId_1.SimplifiedSignalIdSchema.array());
		exports.RelativeSignalNameTypeSchema = zod_1.z.union([zod_1.z.string(), zod_1.z.array(zod_1.z.string())]);
		exports.RelativeSignalNameTypeSimplifiedSchema = zod_1.z.preprocess((val) => {
		    if (Array.isArray(val)) {
		        return val;
		    }
		    return [val];
		}, zod_1.z.array(zod_1.z.string()));
		exports.RelativeSignalSchema = zod_1.z.object({
		    signal: exports.RelativeSignalSignalTypeSchema.optional(),
		    signal_name: exports.RelativeSignalNameTypeSchema.optional(),
		});
		exports.RelativeSignalSimplifiedSchema = zod_1.z.object({
		    signal: exports.RelativeSignalSignalTypeSimplifiedSchema.optional(),
		    signal_name: exports.RelativeSignalNameTypeSimplifiedSchema.optional(),
		});
		
	} (RelativeSignal));
	return RelativeSignal;
}

var hasRequiredIConnectionBoundaryDefinition;

function requireIConnectionBoundaryDefinition () {
	if (hasRequiredIConnectionBoundaryDefinition) return IConnectionBoundaryDefinition;
	hasRequiredIConnectionBoundaryDefinition = 1;
	Object.defineProperty(IConnectionBoundaryDefinition, "__esModule", { value: true });
	IConnectionBoundaryDefinition.IConnectionBoundaryDefinitionSchema = void 0;
	IConnectionBoundaryDefinition.isBoundaryConnectionDefinition = isBoundaryConnectionDefinition;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryAssertionType_1 = requireBoundaryAssertionType();
	const RelativeSignal_1 = requireRelativeSignal();
	IConnectionBoundaryDefinition.IConnectionBoundaryDefinitionSchema = zod_1.z.object({
	    type: zod_1.z.literal(BoundaryAssertionType_1.BoundaryAssertionType.Connection),
	    connection: zod_1.z.array(RelativeSignal_1.RelativeSignalSchema),
	    connectionImpedance: zod_1.z.number().optional(),
	});
	function isBoundaryConnectionDefinition(boundary) {
	    return boundary.type === BoundaryAssertionType_1.BoundaryAssertionType.Connection;
	}
	
	return IConnectionBoundaryDefinition;
}

var INumericalBoundaryDefinition = {};

var BoundarySourceType = {};

var hasRequiredBoundarySourceType;

function requireBoundarySourceType () {
	if (hasRequiredBoundarySourceType) return BoundarySourceType;
	hasRequiredBoundarySourceType = 1;
	Object.defineProperty(BoundarySourceType, "__esModule", { value: true });
	BoundarySourceType.BoundarySourceTypeSchema = BoundarySourceType.BoundarySourceType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundarySourceType$1;
	(function (BoundarySourceType) {
	    BoundarySourceType["Conditional"] = "conditional";
	    BoundarySourceType["Permanent"] = "permanent";
	})(BoundarySourceType$1 || (BoundarySourceType.BoundarySourceType = BoundarySourceType$1 = {}));
	BoundarySourceType.BoundarySourceTypeSchema = zod_1.z.nativeEnum(BoundarySourceType$1);
	
	return BoundarySourceType;
}

var hasRequiredINumericalBoundaryDefinition;

function requireINumericalBoundaryDefinition () {
	if (hasRequiredINumericalBoundaryDefinition) return INumericalBoundaryDefinition;
	hasRequiredINumericalBoundaryDefinition = 1;
	Object.defineProperty(INumericalBoundaryDefinition, "__esModule", { value: true });
	INumericalBoundaryDefinition.INumericalBoundaryDefinitionSchema = void 0;
	INumericalBoundaryDefinition.isBoundaryNumericalDefinition = isBoundaryNumericalDefinition;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryAssertionType_1 = requireBoundaryAssertionType();
	const BoundarySourceType_1 = requireBoundarySourceType();
	const NumericalBoundary_1 = requireNumericalBoundary();
	INumericalBoundaryDefinition.INumericalBoundaryDefinitionSchema = zod_1.z.object({
	    type: zod_1.z.literal(BoundaryAssertionType_1.BoundaryAssertionType.Numerical),
	    sourceType: BoundarySourceType_1.BoundarySourceTypeSchema.optional(),
	    numerical: zod_1.z.lazy(() => NumericalBoundary_1.NumericalBoundarySchema),
	});
	function isBoundaryNumericalDefinition(boundary) {
	    return boundary.type === BoundaryAssertionType_1.BoundaryAssertionType.Numerical;
	}
	
	return INumericalBoundaryDefinition;
}

var IBoundaryBase = {};

var BoundaryTarget = {};

var hasRequiredBoundaryTarget;

function requireBoundaryTarget () {
	if (hasRequiredBoundaryTarget) return BoundaryTarget;
	hasRequiredBoundaryTarget = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BoundaryTargetComponent = exports.BoundaryTargetDesign = exports.BoundaryTargetTypeSchema = exports.BoundaryTargetType = exports.BoundaryTargetSchema = exports.TraversalInTargetSchema = exports.TraversalOutTargetSchema = exports.DesignTargetSchema = exports.ComponentTargetSchema = exports.BoundaryTargetTraversalRootSchema = void 0;
		exports.isBoundaryTargetRelativeSignals = isBoundaryTargetRelativeSignals;
		exports.isBoundaryTargetTraversalIn = isBoundaryTargetTraversalIn;
		exports.isBoundaryTargetTraversalRoot = isBoundaryTargetTraversalRoot;
		exports.getBoundaryTargetRelativeSignals = getBoundaryTargetRelativeSignals;
		exports.boundaryTargetToString = boundaryTargetToString;
		exports.isBoundaryTargetRelativeWithSignal = isBoundaryTargetRelativeWithSignal;
		exports.isSignalInRelativeSignal = isSignalInRelativeSignal;
		exports.getBoundaryTargetSignals = getBoundaryTargetSignals;
		exports.getBoundaryTargetSignalIds = getBoundaryTargetSignalIds;
		const zod_1 = /*@__PURE__*/ requireZod();
		const RelativeSignal_1 = requireRelativeSignal();
		const Utils_1 = requireUtils$4();
		exports.BoundaryTargetTraversalRootSchema = zod_1.z.object({
		    traversalRoot: zod_1.z.literal(true),
		    signal: zod_1.z.string().optional(),
		    functionSignalRef: zod_1.z
		        .object({
		        signal: zod_1.z.string(),
		        function: zod_1.z.string(),
		    })
		        .optional(),
		});
		exports.ComponentTargetSchema = zod_1.z.object({
		    component: zod_1.z.literal(true),
		});
		exports.DesignTargetSchema = zod_1.z.object({
		    design: zod_1.z.literal(true),
		});
		exports.TraversalOutTargetSchema = zod_1.z.object({
		    traversalOut: zod_1.z.literal(true),
		});
		exports.TraversalInTargetSchema = zod_1.z.object({
		    traversalIn: zod_1.z.literal(true),
		});
		exports.BoundaryTargetSchema = zod_1.z.union([
		    RelativeSignal_1.RelativeSignalSchema,
		    exports.ComponentTargetSchema,
		    exports.DesignTargetSchema,
		    exports.TraversalOutTargetSchema,
		    exports.TraversalInTargetSchema,
		    exports.BoundaryTargetTraversalRootSchema,
		]);
		var BoundaryTargetType;
		(function (BoundaryTargetType) {
		    BoundaryTargetType["RelativeSignal"] = "RelativeSignal";
		    BoundaryTargetType["Component"] = "Component";
		    BoundaryTargetType["Design"] = "Design";
		    BoundaryTargetType["TraversalIn"] = "TraversalIn";
		    BoundaryTargetType["TraversalRoot"] = "TraversalRoot";
		})(BoundaryTargetType || (exports.BoundaryTargetType = BoundaryTargetType = {}));
		exports.BoundaryTargetTypeSchema = zod_1.z.nativeEnum(BoundaryTargetType);
		exports.BoundaryTargetDesign = { design: true };
		exports.BoundaryTargetComponent = { component: true };
		function isBoundaryTargetRelativeSignals(target) {
		    return target?.signal_name !== undefined || target?.signal !== undefined;
		}
		function isBoundaryTargetTraversalIn(target) {
		    return target?.traversalIn === true;
		}
		function isBoundaryTargetTraversalRoot(target) {
		    return target?.traversalRoot === true;
		}
		function getBoundaryTargetRelativeSignals(target) {
		    if (isBoundaryTargetRelativeSignals(target)) {
		        return [
		            ...(0, Utils_1.toArray)(target.signal).map((s) => ({ signal: s })),
		            ...(0, Utils_1.toArray)(target.signal_name).map((s) => ({ signal_name: s })),
		        ];
		    }
		    return [];
		}
		function boundaryTargetToString(target) {
		    if (isBoundaryTargetRelativeSignals(target)) {
		        return `RelativeSignal(${[...(0, Utils_1.toArray)(target.signal), ...(0, Utils_1.toArray)(target.signal_name)].join(', ')})`;
		    }
		    if (target === exports.BoundaryTargetDesign) {
		        return 'Design';
		    }
		    if (target === exports.BoundaryTargetComponent) {
		        return 'Component';
		    }
		    if (isBoundaryTargetTraversalIn(target)) {
		        return 'TraversalIn';
		    }
		    if (isBoundaryTargetTraversalRoot(target)) {
		        return 'TraversalRoot';
		    }
		    return 'Unknown';
		}
		function isBoundaryTargetRelativeWithSignal(target, signal) {
		    return !!target && isSignalInRelativeSignal(target, signal);
		}
		function isSignalInRelativeSignal(target, signal) {
		    if (target.signal !== undefined) {
		        return (Array.isArray(target.signal) && target.signal.indexOf(signal.id) !== -1) || target.signal === signal.id;
		    }
		    return (target.signal_name !== undefined &&
		        ((Array.isArray(target.signal_name) && target.signal_name.indexOf(signal.name) !== -1) ||
		            target.signal_name === signal.name));
		}
		function getBoundaryTargetSignals(signals, target) {
		    if (target && isBoundaryTargetRelativeSignals(target)) {
		        return Array.from(new Set([
		            ...(0, Utils_1.toArray)(target.signal).map((id) => signals.find((signal) => signal.id === id)),
		            ...(0, Utils_1.toArray)(target.signal_name).map((s) => signals.find((signal) => signal.name === s)),
		        ])).filter((x) => x !== undefined);
		    }
		    return [];
		}
		function getBoundaryTargetSignalIds(signals, target) {
		    if (target && isBoundaryTargetRelativeSignals(target)) {
		        return Array.from(new Set([
		            ...(0, Utils_1.toArray)(target.signal),
		            ...(0, Utils_1.toArray)(target.signal_name)
		                .map((s) => signals.find((signal) => signal.name === s)?.id)
		                .filter(Boolean),
		        ]));
		    }
		    return [];
		}
		
	} (BoundaryTarget));
	return BoundaryTarget;
}

var hasRequiredIBoundaryBase;

function requireIBoundaryBase () {
	if (hasRequiredIBoundaryBase) return IBoundaryBase;
	hasRequiredIBoundaryBase = 1;
	Object.defineProperty(IBoundaryBase, "__esModule", { value: true });
	IBoundaryBase.IBoundaryBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryAssertionStrength_1 = requireBoundaryAssertionStrength();
	const BoundaryLevel_1 = requireBoundaryLevel();
	const BoundaryTarget_1 = requireBoundaryTarget();
	IBoundaryBase.IBoundaryBaseSchema = zod_1.z.object({
	    strength: BoundaryAssertionStrength_1.BoundaryAssertionStrengthSchema.optional(),
	    target: BoundaryTarget_1.BoundaryTargetSchema,
	    level: BoundaryLevel_1.BoundaryLevelSchema.optional(),
	    relative_to: BoundaryTarget_1.BoundaryTargetSchema.optional(),
	    class: zod_1.z.string().optional(),
	});
	
	return IBoundaryBase;
}

var hasRequiredIBoundaryDefinition;

function requireIBoundaryDefinition () {
	if (hasRequiredIBoundaryDefinition) return IBoundaryDefinition;
	hasRequiredIBoundaryDefinition = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IBoundaryDefinitionWithIdSchema = exports.IBoundaryDefinitionWithIdSchema_ConnectionDefinition = exports.IBoundaryDefinitionWithIdSchema_NumericalDefinition = exports.IBoundaryDefinitionSchema = exports.IBoundaryDefinitionSchema_ConnectionDefinition = exports.IBoundaryDefinitionSchema_NumericalDefinition = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IConnectionBoundaryDefinition_1 = requireIConnectionBoundaryDefinition();
		const INumericalBoundaryDefinition_1 = requireINumericalBoundaryDefinition();
		const IBoundaryBase_1 = requireIBoundaryBase();
		exports.IBoundaryDefinitionSchema_NumericalDefinition = INumericalBoundaryDefinition_1.INumericalBoundaryDefinitionSchema.merge(IBoundaryBase_1.IBoundaryBaseSchema);
		exports.IBoundaryDefinitionSchema_ConnectionDefinition = IConnectionBoundaryDefinition_1.IConnectionBoundaryDefinitionSchema.merge(IBoundaryBase_1.IBoundaryBaseSchema);
		exports.IBoundaryDefinitionSchema = zod_1.z.discriminatedUnion('type', [
		    exports.IBoundaryDefinitionSchema_NumericalDefinition,
		    exports.IBoundaryDefinitionSchema_ConnectionDefinition,
		]);
		exports.IBoundaryDefinitionWithIdSchema_NumericalDefinition = INumericalBoundaryDefinition_1.INumericalBoundaryDefinitionSchema.merge(IBoundaryBase_1.IBoundaryBaseSchema).extend({
		    boundaryId: zod_1.z.string(),
		    boundaryGlobalId: zod_1.z.string(),
		});
		exports.IBoundaryDefinitionWithIdSchema_ConnectionDefinition = IConnectionBoundaryDefinition_1.IConnectionBoundaryDefinitionSchema.merge(IBoundaryBase_1.IBoundaryBaseSchema).extend({
		    boundaryId: zod_1.z.string(),
		    boundaryGlobalId: zod_1.z.string(),
		});
		exports.IBoundaryDefinitionWithIdSchema = zod_1.z.discriminatedUnion('type', [
		    exports.IBoundaryDefinitionWithIdSchema_NumericalDefinition,
		    exports.IBoundaryDefinitionWithIdSchema_ConnectionDefinition,
		]);
		
	} (IBoundaryDefinition));
	return IBoundaryDefinition;
}

var hasRequiredModeBoundaryCondition;

function requireModeBoundaryCondition () {
	if (hasRequiredModeBoundaryCondition) return ModeBoundaryCondition;
	hasRequiredModeBoundaryCondition = 1;
	Object.defineProperty(ModeBoundaryCondition, "__esModule", { value: true });
	ModeBoundaryCondition.ModeBoundaryConditionSchema = void 0;
	ModeBoundaryCondition.isModeBoundaryCondition = isModeBoundaryCondition;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IBoundaryDefinition_1 = requireIBoundaryDefinition();
	ModeBoundaryCondition.ModeBoundaryConditionSchema = zod_1.z.object({
	    boundary: zod_1.z.lazy(() => IBoundaryDefinition_1.IBoundaryDefinitionSchema),
	});
	function isModeBoundaryCondition(condition) {
	    return condition.boundary !== undefined;
	}
	
	return ModeBoundaryCondition;
}

var ModeReferenceCondition = {};

var hasRequiredModeReferenceCondition;

function requireModeReferenceCondition () {
	if (hasRequiredModeReferenceCondition) return ModeReferenceCondition;
	hasRequiredModeReferenceCondition = 1;
	Object.defineProperty(ModeReferenceCondition, "__esModule", { value: true });
	ModeReferenceCondition.ModeReferenceConditionSchema = void 0;
	ModeReferenceCondition.isModeReferenceCondition = isModeReferenceCondition;
	const zod_1 = /*@__PURE__*/ requireZod();
	ModeReferenceCondition.ModeReferenceConditionSchema = zod_1.z.object({
	    mode: zod_1.z.string(),
	    mode_state: zod_1.z.boolean(),
	});
	function isModeReferenceCondition(condition) {
	    return condition.mode !== undefined;
	}
	
	return ModeReferenceCondition;
}

var ModeSignalLogicalStateCondition = {};

var hasRequiredModeSignalLogicalStateCondition;

function requireModeSignalLogicalStateCondition () {
	if (hasRequiredModeSignalLogicalStateCondition) return ModeSignalLogicalStateCondition;
	hasRequiredModeSignalLogicalStateCondition = 1;
	Object.defineProperty(ModeSignalLogicalStateCondition, "__esModule", { value: true });
	ModeSignalLogicalStateCondition.ModeSignalLogicalStateConditionSchema = void 0;
	ModeSignalLogicalStateCondition.isModeSignalLogicalStateCondition = isModeSignalLogicalStateCondition;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SignalId_1 = requireSignalId();
	ModeSignalLogicalStateCondition.ModeSignalLogicalStateConditionSchema = zod_1.z.object({
	    signalId: SignalId_1.SignalIdSchema,
	});
	function isModeSignalLogicalStateCondition(condition) {
	    return condition.signalId !== undefined;
	}
	
	return ModeSignalLogicalStateCondition;
}

var hasRequiredIModeCondition;

function requireIModeCondition () {
	if (hasRequiredIModeCondition) return IModeCondition;
	hasRequiredIModeCondition = 1;
	Object.defineProperty(IModeCondition, "__esModule", { value: true });
	IModeCondition.IModeConditionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ModeBoundaryCondition_1 = requireModeBoundaryCondition();
	const ModeReferenceCondition_1 = requireModeReferenceCondition();
	const ModeSignalLogicalStateCondition_1 = requireModeSignalLogicalStateCondition();
	IModeCondition.IModeConditionSchema = zod_1.z.union([
	    ModeBoundaryCondition_1.ModeBoundaryConditionSchema,
	    ModeReferenceCondition_1.ModeReferenceConditionSchema,
	    ModeSignalLogicalStateCondition_1.ModeSignalLogicalStateConditionSchema,
	]);
	
	return IModeCondition;
}

var hasRequiredIExternalComponetConnection;

function requireIExternalComponetConnection () {
	if (hasRequiredIExternalComponetConnection) return IExternalComponetConnection;
	hasRequiredIExternalComponetConnection = 1;
	Object.defineProperty(IExternalComponetConnection, "__esModule", { value: true });
	IExternalComponetConnection.IExternalComponetConnectionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IModeCondition_1 = requireIModeCondition();
	const IExternalComponent_1 = requireIExternalComponent();
	IExternalComponetConnection.IExternalComponetConnectionSchema = zod_1.z.object({
	    name: zod_1.z.string(),
	    modelSignalId: zod_1.z.string().optional(),
	    netConditions: zod_1.z.lazy(() => IModeCondition_1.IModeConditionSchema.array().optional()),
	    components: zod_1.z
	        .lazy(() => IExternalComponent_1.IExternalComponentSchema)
	        .array()
	        .optional(),
	});
	
	return IExternalComponetConnection;
}

var hasRequiredIExternalComponent;

function requireIExternalComponent () {
	if (hasRequiredIExternalComponent) return IExternalComponent;
	hasRequiredIExternalComponent = 1;
	Object.defineProperty(IExternalComponent, "__esModule", { value: true });
	IExternalComponent.IExternalComponentSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryLevel_1 = requireBoundaryLevel();
	const NumericalBoundary_1 = requireNumericalBoundary();
	const FunctionReferenceCondition_1 = requireFunctionReferenceCondition();
	const TraversalHelpers_1 = requireTraversalHelpers();
	const IExternalComponetConnection_1 = requireIExternalComponetConnection();
	const TraversalSolvableCharacteristic_1 = requireTraversalSolvableCharacteristic();
	IExternalComponent.IExternalComponentSchema = zod_1.z.object({
	    name: zod_1.z.string(),
	    model: zod_1.z.string().optional(),
	    rootComponentSignalId: zod_1.z.string().optional(),
	    traversalType: zod_1.z
	        .union([TraversalSolvableCharacteristic_1.TraversalSolvableCharacteristicSchema, TraversalHelpers_1.TraversalSolvableCharacteristicMatcherSchema])
	        .optional(),
	    functionClass: zod_1.z.string().optional(),
	    functionSignal: zod_1.z.string().optional(),
	    isPassive: zod_1.z.boolean().optional(),
	    count: zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal('many')]).optional(),
	    value: NumericalBoundary_1.NumericalBoundarySchema.optional(),
	    connectedFunction: FunctionReferenceCondition_1.FunctionReferenceConditionSchema.optional(),
	    valueLevel: BoundaryLevel_1.BoundaryLevelSchema.optional(),
	    withConnections: zod_1.z
	        .lazy(() => IExternalComponetConnection_1.IExternalComponetConnectionSchema)
	        .array()
	        .optional(),
	});
	
	return IExternalComponent;
}

var hasRequiredFunctionalAddressing;

function requireFunctionalAddressing () {
	if (hasRequiredFunctionalAddressing) return FunctionalAddressing;
	hasRequiredFunctionalAddressing = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ControllerSignalMappingSchema = exports.AddressingStateSchema = exports.AddressingStateType = exports.ControllerBusAddressSchema = exports.BusAddressingMethodSchema = exports.DeviceSelectSchema = exports.ProtocolAddressSchema = exports.ConfigurableProtocolAddressSchema = exports.ConfigurableProtocolAddressSignalSchema = exports.SelectableProtocolAddressSchema = exports.FixedProtocolAddressSchema = exports.PinState = void 0;
		exports.getAddressingStateString = getAddressingStateString;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IExternalComponent_1 = requireIExternalComponent();
		var PinState;
		(function (PinState) {
		    PinState["High"] = "HIGH";
		    PinState["Low"] = "LOW";
		})(PinState || (exports.PinState = PinState = {}));
		exports.FixedProtocolAddressSchema = zod_1.z.object({
		    type: zod_1.z.literal('fixed'),
		    address: zod_1.z.number(),
		});
		exports.SelectableProtocolAddressSchema = zod_1.z.object({
		    type: zod_1.z.literal('selectable'),
		    selectSignalId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
		    addresses: zod_1.z.object({
		        highState: zod_1.z.number(),
		        lowState: zod_1.z.number(),
		    }),
		});
		exports.ConfigurableProtocolAddressSignalSchema = zod_1.z.object({
		    signalId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
		    bitPosition: zod_1.z.number(),
		    bitValue: zod_1.z.number().optional(),
		    activeState: zod_1.z.union([
		        zod_1.z.nativeEnum(PinState),
		        zod_1.z.object({
		            component: zod_1.z.lazy(() => IExternalComponent_1.IExternalComponentSchema),
		        }),
		    ]),
		});
		exports.ConfigurableProtocolAddressSchema = zod_1.z.object({
		    type: zod_1.z.literal('configurable'),
		    baseAddress: zod_1.z.number(),
		    configurableSignals: zod_1.z.array(zod_1.z.lazy(() => exports.ConfigurableProtocolAddressSignalSchema)),
		});
		exports.ProtocolAddressSchema = zod_1.z.discriminatedUnion('type', [
		    exports.FixedProtocolAddressSchema,
		    exports.SelectableProtocolAddressSchema,
		    exports.ConfigurableProtocolAddressSchema,
		]);
		exports.DeviceSelectSchema = zod_1.z.object({
		    selectSignalId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
		    activeState: zod_1.z.nativeEnum(PinState),
		});
		exports.BusAddressingMethodSchema = zod_1.z.enum(['protocol', 'select']);
		exports.ControllerBusAddressSchema = zod_1.z.object({
		    moduleName: zod_1.z.string(),
		    signalsToDrive: zod_1.z.array(zod_1.z.lazy(() => exports.ControllerSignalMappingSchema)),
		});
		var AddressingStateType;
		(function (AddressingStateType) {
		    AddressingStateType["PinState"] = "pinState";
		    AddressingStateType["Address"] = "address";
		})(AddressingStateType || (exports.AddressingStateType = AddressingStateType = {}));
		exports.AddressingStateSchema = zod_1.z.discriminatedUnion('type', [
		    zod_1.z.object({ type: zod_1.z.literal(AddressingStateType.PinState), pinState: zod_1.z.nativeEnum(PinState) }),
		    zod_1.z.object({ type: zod_1.z.literal(AddressingStateType.Address), address: zod_1.z.number() }),
		]);
		exports.ControllerSignalMappingSchema = zod_1.z.object({
		    controllerSignalId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
		    state: exports.AddressingStateSchema,
		});
		function getAddressingStateString(state) {
		    if (!state) {
		        throw new Error('Addressing state is not defined');
		    }
		    if ('pinState' in state) {
		        return state.pinState;
		    }
		    return `0x${state.address.toString(16)}`;
		}
		
	} (FunctionalAddressing));
	return FunctionalAddressing;
}

var IComponentFunctionSignalAssignment = {};

var SignalDriveMode = {};

var hasRequiredSignalDriveMode;

function requireSignalDriveMode () {
	if (hasRequiredSignalDriveMode) return SignalDriveMode;
	hasRequiredSignalDriveMode = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SignalDriveModeDefinitionSchema = exports.DriveModeValueParamsSchema = exports.ISignalDriveModeConditionSchema = exports.ISignalDriveBiasSchema = exports.IVoltageReferenceBiasSchema = exports.ISignalReferenceBiasSchema = exports.SignalDriveBiasBaseSchema = exports.SignalDriveTypeSchema = exports.SignalInputDriveType = exports.SignalOutputDriveType = void 0;
		const RelativeSignal_1 = requireRelativeSignal();
		const zod_1 = /*@__PURE__*/ requireZod();
		const zod_field_presence_1 = requireZodFieldPresence();
		var SignalOutputDriveType;
		(function (SignalOutputDriveType) {
		    SignalOutputDriveType["OpenDrain"] = "open-drain";
		    SignalOutputDriveType["OpenSource"] = "open-source";
		    SignalOutputDriveType["OpenCollector"] = "open-collector";
		    SignalOutputDriveType["OpenEmitter"] = "open-emitter";
		    SignalOutputDriveType["PushPull"] = "push-pull";
		})(SignalOutputDriveType || (exports.SignalOutputDriveType = SignalOutputDriveType = {}));
		var SignalInputDriveType;
		(function (SignalInputDriveType) {
		    SignalInputDriveType["HighZ"] = "high-z";
		})(SignalInputDriveType || (exports.SignalInputDriveType = SignalInputDriveType = {}));
		exports.SignalDriveTypeSchema = zod_1.z.nativeEnum({ ...SignalOutputDriveType, ...SignalInputDriveType });
		exports.SignalDriveBiasBaseSchema = zod_1.z.object({
		    impedance: (0, zod_field_presence_1.coercedString)(),
		});
		exports.ISignalReferenceBiasSchema = zod_1.z.object({
		    type: zod_1.z.literal('signal'),
		    impedance: (0, zod_field_presence_1.coercedString)(),
		    signalReference: RelativeSignal_1.RelativeSignalSchema,
		});
		exports.IVoltageReferenceBiasSchema = zod_1.z.object({
		    type: zod_1.z.literal('voltage'),
		    impedance: (0, zod_field_presence_1.coercedString)(),
		    voltageReference: zod_1.z.number(),
		});
		exports.ISignalDriveBiasSchema = zod_1.z.discriminatedUnion('type', [
		    exports.ISignalReferenceBiasSchema,
		    exports.IVoltageReferenceBiasSchema,
		]);
		exports.ISignalDriveModeConditionSchema = zod_1.z.object({
		    name: zod_1.z.string().optional(),
		    description: zod_1.z.string().optional(),
		    condition: (0, zod_field_presence_1.allowingAbsentKey)(zod_1.z.custom()),
		});
		exports.DriveModeValueParamsSchema = zod_1.z.object({
		    outputHighVoltage: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		    outputLowVoltage: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		    inputHighVoltage: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		    inputLowVoltage: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		    internalPullupBias: exports.ISignalDriveBiasSchema.optional(),
		    internalPulldownBias: exports.ISignalDriveBiasSchema.optional(),
		    outputImpedance: zod_1.z.coerce.string().optional(),
		});
		exports.SignalDriveModeDefinitionSchema = zod_1.z.object({
		    type: exports.SignalDriveTypeSchema,
		    parameters: exports.DriveModeValueParamsSchema.optional(),
		    conditions: zod_1.z.array(exports.ISignalDriveModeConditionSchema).optional(),
		});
		
	} (SignalDriveMode));
	return SignalDriveMode;
}

var hasRequiredIComponentFunctionSignalAssignment;

function requireIComponentFunctionSignalAssignment () {
	if (hasRequiredIComponentFunctionSignalAssignment) return IComponentFunctionSignalAssignment;
	hasRequiredIComponentFunctionSignalAssignment = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IComponentFunctionSignalAssignmentSchema = exports.IActiveDriveModeSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SignalId_1 = requireSignalId();
		const SignalDriveMode_1 = requireSignalDriveMode();
		exports.IActiveDriveModeSchema = zod_1.z.object({
		    type: zod_1.z.nativeEnum({ ...SignalDriveMode_1.SignalOutputDriveType, ...SignalDriveMode_1.SignalInputDriveType }),
		    pullupEnable: zod_1.z.boolean().optional(),
		    pulldownEnable: zod_1.z.boolean().optional(),
		});
		exports.IComponentFunctionSignalAssignmentSchema = zod_1.z.object({
		    componentSignalId: SignalId_1.SignalIdSchema,
		    activeDriveModes: zod_1.z.array(exports.IActiveDriveModeSchema).optional(),
		});
		
	} (IComponentFunctionSignalAssignment));
	return IComponentFunctionSignalAssignment;
}

var hasRequiredISerializedTraversal;

function requireISerializedTraversal () {
	if (hasRequiredISerializedTraversal) return ISerializedTraversal;
	hasRequiredISerializedTraversal = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISerializedComponentFunctionAssignmentSchema = exports.ISerializedExternalComponentAssignmentSchema = exports.ISerializedTraversalSchema = void 0;
		exports.createSerializedTraversalSchema = createSerializedTraversalSchema;
		const zod_1 = /*@__PURE__*/ requireZod();
		const FunctionalAddressing_1 = requireFunctionalAddressing();
		const IComponentFunctionSignalAssignment_1 = requireIComponentFunctionSignalAssignment();
		function createSerializedTraversalSchema(metadataSchema) {
		    return zod_1.z.object({
		        path: zod_1.z.string(),
		        metadata: zod_1.z.record(zod_1.z.coerce.number(), metadataSchema ?? zod_1.z.object({}).passthrough()).optional(),
		    });
		}
		exports.ISerializedTraversalSchema = createSerializedTraversalSchema();
		exports.ISerializedExternalComponentAssignmentSchema = zod_1.z.object({
		    assignment: zod_1.z.string(),
		    traversals: zod_1.z.array(exports.ISerializedTraversalSchema),
		});
		exports.ISerializedComponentFunctionAssignmentSchema = zod_1.z.lazy(() => zod_1.z.object({
		    function: zod_1.z.string(),
		    functionClass: zod_1.z.string().optional(),
		    isUnassigned: zod_1.z.boolean().optional(),
		    signalAssignments: zod_1.z.record(zod_1.z.string(), IComponentFunctionSignalAssignment_1.IComponentFunctionSignalAssignmentSchema.array()),
		    externalComponentAssignments: zod_1.z.record(zod_1.z.string(), zod_1.z.record(zod_1.z.string(), exports.ISerializedExternalComponentAssignmentSchema.array())),
		    addressableComponents: FunctionalAddressing_1.ControllerBusAddressSchema.array().optional(),
		    externalConnectionAssignments: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    childBusAssignments: zod_1.z
		        .record(zod_1.z.string(), zod_1.z.lazy(() => exports.ISerializedComponentFunctionAssignmentSchema))
		        .optional(),
		}));
		
	} (ISerializedTraversal));
	return ISerializedTraversal;
}

var IComponentResource = {};

var metadata = {};

var hasRequiredMetadata;

function requireMetadata () {
	if (hasRequiredMetadata) return metadata;
	hasRequiredMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ObjectWithMetadataSchema = exports.MetadataSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.MetadataSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional(), zod_1.z.undefined()]));
		exports.ObjectWithMetadataSchema = zod_1.z
		    .object({
		    metadata: exports.MetadataSchema.optional(),
		})
		    .catchall(zod_1.z.any());
		
	} (metadata));
	return metadata;
}

var hasRequiredIComponentResource;

function requireIComponentResource () {
	if (hasRequiredIComponentResource) return IComponentResource;
	hasRequiredIComponentResource = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentResourceFunctionConsumptionSchema = exports.IComponentResourceInstanceSchema = exports.IComponentResourceSchema = void 0;
		const IModeCondition_1 = requireIModeCondition();
		const metadata_1 = requireMetadata();
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.IComponentResourceSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    conditions: zod_1.z.lazy(() => IModeCondition_1.IModeConditionSchema.array().optional()),
		    mustBeConsumed: zod_1.z.boolean().optional(),
		    metadata: metadata_1.MetadataSchema.optional(),
		});
		exports.IComponentResourceInstanceSchema = exports.IComponentResourceSchema.extend({
		    id: zod_1.z.string(),
		    componentName: zod_1.z.string(),
		    moduleName: zod_1.z.string(),
		});
		exports.ComponentResourceFunctionConsumptionSchema = zod_1.z.object({
		    resource: zod_1.z.string(),
		    function: zod_1.z.string(),
		});
		
	} (IComponentResource));
	return IComponentResource;
}

var hasRequiredComponentFunctionStatesSerialized;

function requireComponentFunctionStatesSerialized () {
	if (hasRequiredComponentFunctionStatesSerialized) return ComponentFunctionStatesSerialized;
	hasRequiredComponentFunctionStatesSerialized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SerializedComponentFunctionsMapSchema = exports.ISerializedComponentFunctionStateSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ISerializedTraversal_1 = requireISerializedTraversal();
		const IComponentResource_1 = requireIComponentResource();
		exports.ISerializedComponentFunctionStateSchema = zod_1.default.object({
		    moduleName: zod_1.default.string(),
		    componentName: zod_1.default.string(),
		    resourceConsumption: zod_1.default.record(zod_1.default.string(), IComponentResource_1.ComponentResourceFunctionConsumptionSchema),
		    functionAssignments: zod_1.default.record(zod_1.default.string(), ISerializedTraversal_1.ISerializedComponentFunctionAssignmentSchema),
		});
		exports.SerializedComponentFunctionsMapSchema = zod_1.default.record(zod_1.default.string(), exports.ISerializedComponentFunctionStateSchema);
		
	} (ComponentFunctionStatesSerialized));
	return ComponentFunctionStatesSerialized;
}

var ISerializedBoundaryConfiguration = {};

var SerializedBoundaryState = {};

var BoundaryErrorsDefinition = {};

var IBoundaryInstance = {};

var ComponentBoundary = {};

var hasRequiredComponentBoundary;

function requireComponentBoundary () {
	if (hasRequiredComponentBoundary) return ComponentBoundary;
	hasRequiredComponentBoundary = 1;
	Object.defineProperty(ComponentBoundary, "__esModule", { value: true });
	ComponentBoundary.ComponentBoundarySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IBoundaryDefinition_1 = requireIBoundaryDefinition();
	const IModeCondition_1 = requireIModeCondition();
	ComponentBoundary.ComponentBoundarySchema = zod_1.z.object({
	    id: zod_1.z.string(),
	    global: zod_1.z.boolean().optional(),
	    conditions: IModeCondition_1.IModeConditionSchema.array().optional(),
	    boundary: IBoundaryDefinition_1.IBoundaryDefinitionSchema,
	});
	
	return ComponentBoundary;
}

var hasRequiredIBoundaryInstance;

function requireIBoundaryInstance () {
	if (hasRequiredIBoundaryInstance) return IBoundaryInstance;
	hasRequiredIBoundaryInstance = 1;
	Object.defineProperty(IBoundaryInstance, "__esModule", { value: true });
	IBoundaryInstance.BoundaryGlobalIdRefSchema = IBoundaryInstance.IBoundaryInstanceSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ComponentBoundary_1 = requireComponentBoundary();
	IBoundaryInstance.IBoundaryInstanceSchema = ComponentBoundary_1.ComponentBoundarySchema.extend({
	    moduleName: zod_1.z.string().optional(),
	    functionName: zod_1.z.string().optional(),
	    globalId: zod_1.z.string(),
	});
	IBoundaryInstance.BoundaryGlobalIdRefSchema = zod_1.z
	    .union([zod_1.z.string(), zod_1.z.object({ globalId: zod_1.z.string() }).passthrough()])
	    .transform((v) => (typeof v === 'string' ? v : v.globalId));
	
	return IBoundaryInstance;
}

var hasRequiredBoundaryErrorsDefinition;

function requireBoundaryErrorsDefinition () {
	if (hasRequiredBoundaryErrorsDefinition) return BoundaryErrorsDefinition;
	hasRequiredBoundaryErrorsDefinition = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BoundaryErrorTypeSchema = exports.BoundaryErrorType = exports.IBoundaryValueWithSourceSchema = exports.IBoundaryErrorSchema = exports.IBoundaryErrorCircuitObjectSchema = exports.IBoundaryErrorCircuitObjectNetConnectionSchema = exports.IBoundaryErrorCircuitObjectComponentSchema = exports.IBoundaryErrorCircuitObjectModuleSchema = exports.IBoundaryErrorCircuitObjectNetSchema = exports.IBoundaryErrorCircuitObjectBaseSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IBoundaryInstance_1 = requireIBoundaryInstance();
		const BoundaryLevel_1 = requireBoundaryLevel();
		const ISerializedCircuitState_1 = requireISerializedCircuitState();
		exports.IBoundaryErrorCircuitObjectBaseSchema = zod_1.z.object({
		    type: zod_1.z.string(),
		});
		exports.IBoundaryErrorCircuitObjectNetSchema = exports.IBoundaryErrorCircuitObjectBaseSchema.extend({
		    type: zod_1.z.literal('net'),
		    net: zod_1.z.string(),
		});
		exports.IBoundaryErrorCircuitObjectModuleSchema = exports.IBoundaryErrorCircuitObjectBaseSchema.extend({
		    type: zod_1.z.literal('module'),
		    module: zod_1.z.string(),
		});
		exports.IBoundaryErrorCircuitObjectComponentSchema = exports.IBoundaryErrorCircuitObjectBaseSchema.extend({
		    type: zod_1.z.literal('component'),
		    component: zod_1.z.string(),
		});
		const signalStringSchema = zod_1.z
		    .union([zod_1.z.string(), zod_1.z.array(zod_1.z.string())])
		    .transform((val) => (Array.isArray(val) ? (val[0] ?? '') : val));
		exports.IBoundaryErrorCircuitObjectNetConnectionSchema = exports.IBoundaryErrorCircuitObjectBaseSchema.extend({
		    type: zod_1.z.literal('netconn'),
		    module: zod_1.z.string(),
		    signal: signalStringSchema,
		    net: zod_1.z.string(),
		});
		exports.IBoundaryErrorCircuitObjectSchema = zod_1.z.discriminatedUnion('type', [
		    exports.IBoundaryErrorCircuitObjectNetSchema,
		    exports.IBoundaryErrorCircuitObjectModuleSchema,
		    exports.IBoundaryErrorCircuitObjectComponentSchema,
		    exports.IBoundaryErrorCircuitObjectNetConnectionSchema,
		]);
		exports.IBoundaryErrorSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    errorId: zod_1.z.string(),
		    level: BoundaryLevel_1.BoundaryLevelSchema,
		    message: zod_1.z.string(),
		    boundary: IBoundaryInstance_1.IBoundaryInstanceSchema,
		    circuitState: zod_1.z.lazy(() => ISerializedCircuitState_1.SerializedCircuitStateOrPointerSchema).optional(),
		    circuitObjects: zod_1.z
		        .lazy(() => exports.IBoundaryErrorCircuitObjectSchema)
		        .array()
		        .optional(),
		    targetNet: zod_1.z.string().optional(),
		    relativeNet: zod_1.z.string().optional(),
		    actualVal: zod_1.z
		        .union([
		        zod_1.z.number(),
		        zod_1.z.object({
		            value: zod_1.z.number(),
		            source: exports.IBoundaryErrorCircuitObjectSchema,
		        }),
		    ])
		        .optional(),
		});
		exports.IBoundaryValueWithSourceSchema = zod_1.z.object({
		    value: zod_1.z.number(),
		    source: exports.IBoundaryErrorCircuitObjectSchema,
		});
		var BoundaryErrorType;
		(function (BoundaryErrorType) {
		    BoundaryErrorType["MaxValExceeded"] = "BoundaryMaxValExceeded";
		    BoundaryErrorType["MinValNotReached"] = "BoundaryMinValExceeded";
		    BoundaryErrorType["AbsoluteValNotEqual"] = "BoundaryAbsoluteValNotEqual";
		    BoundaryErrorType["SignalReferenceError"] = "BoundarySignalReferenceError";
		})(BoundaryErrorType || (exports.BoundaryErrorType = BoundaryErrorType = {}));
		exports.BoundaryErrorTypeSchema = zod_1.z.nativeEnum(BoundaryErrorType);
		
	} (BoundaryErrorsDefinition));
	return BoundaryErrorsDefinition;
}

var hasRequiredSerializedBoundaryState;

function requireSerializedBoundaryState () {
	if (hasRequiredSerializedBoundaryState) return SerializedBoundaryState;
	hasRequiredSerializedBoundaryState = 1;
	Object.defineProperty(SerializedBoundaryState, "__esModule", { value: true });
	SerializedBoundaryState.SerializedBoundaryStateSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryCharacteristicStateSummaryExternal_1 = requireBoundaryCharacteristicStateSummaryExternal();
	const BoundaryErrorsDefinition_1 = requireBoundaryErrorsDefinition();
	SerializedBoundaryState.SerializedBoundaryStateSchema = zod_1.default.object({
	    characteristicValues: BoundaryCharacteristicStateSummaryExternal_1.BoundaryCharacteristicStateSummaryExternalSchema.array(),
	    insightsErrors: zod_1.default.lazy(() => BoundaryErrorsDefinition_1.IBoundaryErrorSchema).array(),
	    isPartialResolution: zod_1.default.boolean().optional(),
	});
	
	return SerializedBoundaryState;
}

var hasRequiredISerializedBoundaryConfiguration;

function requireISerializedBoundaryConfiguration () {
	if (hasRequiredISerializedBoundaryConfiguration) return ISerializedBoundaryConfiguration;
	hasRequiredISerializedBoundaryConfiguration = 1;
	Object.defineProperty(ISerializedBoundaryConfiguration, "__esModule", { value: true });
	ISerializedBoundaryConfiguration.ISerializedBoundaryConfigurationSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SerializedBoundaryState_1 = requireSerializedBoundaryState();
	const IBoundaryInstance_1 = requireIBoundaryInstance();
	ISerializedBoundaryConfiguration.ISerializedBoundaryConfigurationSchema = zod_1.default.object({
	    netName: zod_1.default.string(),
	    assertingBoundaries: IBoundaryInstance_1.BoundaryGlobalIdRefSchema.array(),
	    connectedNets: zod_1.default.record(zod_1.default.string(), zod_1.default.string().array()),
	    resolvedState: zod_1.default.lazy(() => SerializedBoundaryState_1.SerializedBoundaryStateSchema).optional(),
	    hasResolvedState: zod_1.default.boolean(),
	});
	
	return ISerializedBoundaryConfiguration;
}

var SerializedModeState = {};

var ModeStateType = {};

var hasRequiredModeStateType;

function requireModeStateType () {
	if (hasRequiredModeStateType) return ModeStateType;
	hasRequiredModeStateType = 1;
	Object.defineProperty(ModeStateType, "__esModule", { value: true });
	ModeStateType.ModeStateTypeSchema = ModeStateType.ModeStateType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var ModeStateType$1;
	(function (ModeStateType) {
	    ModeStateType["Indeterminate"] = "indeterminate";
	    ModeStateType["LogicLow"] = "logic-low";
	    ModeStateType["LogicHigh"] = "logic-high";
	})(ModeStateType$1 || (ModeStateType.ModeStateType = ModeStateType$1 = {}));
	ModeStateType.ModeStateTypeSchema = zod_1.z.nativeEnum(ModeStateType$1);
	
	return ModeStateType;
}

var hasRequiredSerializedModeState;

function requireSerializedModeState () {
	if (hasRequiredSerializedModeState) return SerializedModeState;
	hasRequiredSerializedModeState = 1;
	Object.defineProperty(SerializedModeState, "__esModule", { value: true });
	SerializedModeState.SerializedModeStateSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ModeStateType_1 = requireModeStateType();
	SerializedModeState.SerializedModeStateSchema = zod_1.default.union([
	    zod_1.default.boolean(),
	    zod_1.default.literal(ModeStateType_1.ModeStateType.Indeterminate),
	]);
	
	return SerializedModeState;
}

var hasRequiredISerializedCircuitState;

function requireISerializedCircuitState () {
	if (hasRequiredISerializedCircuitState) return ISerializedCircuitState;
	hasRequiredISerializedCircuitState = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SerializedCircuitStateOrPointerSchema = exports.ISerializedCircuitStatePointerSchema = exports.SerializedCircuitStateMapSchema = exports.ISerializedCircuitStateSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryCharacteristicStateSummaryExternal_1 = requireBoundaryCharacteristicStateSummaryExternal();
		const ComponentFunctionStatesSerialized_1 = requireComponentFunctionStatesSerialized();
		const ISerializedBoundaryConfiguration_1 = requireISerializedBoundaryConfiguration();
		const SerializedModeState_1 = requireSerializedModeState();
		exports.ISerializedCircuitStateSchema = zod_1.default.object({
		    modes: zod_1.default.record(zod_1.default.string(), SerializedModeState_1.SerializedModeStateSchema),
		    nets: zod_1.default.record(zod_1.default.string(), zod_1.default.lazy(() => BoundaryCharacteristicStateSummaryExternal_1.BoundaryCharacteristicStateSummaryExternalSchema).array()),
		    functionStates: zod_1.default.lazy(() => ComponentFunctionStatesSerialized_1.SerializedComponentFunctionsMapSchema),
		    boundaries: zod_1.default.record(zod_1.default.string(), zod_1.default.lazy(() => ISerializedBoundaryConfiguration_1.ISerializedBoundaryConfigurationSchema)),
		});
		exports.SerializedCircuitStateMapSchema = zod_1.default.record(zod_1.default.string(), exports.ISerializedCircuitStateSchema);
		exports.ISerializedCircuitStatePointerSchema = zod_1.default.object({
		    csId: zod_1.default.string(),
		});
		exports.SerializedCircuitStateOrPointerSchema = zod_1.default.union([
		    exports.ISerializedCircuitStateSchema,
		    exports.ISerializedCircuitStatePointerSchema,
		]);
		
	} (ISerializedCircuitState));
	return ISerializedCircuitState;
}

var hasRequiredBoundaryCharacteristicStateSerialized;

function requireBoundaryCharacteristicStateSerialized () {
	if (hasRequiredBoundaryCharacteristicStateSerialized) return BoundaryCharacteristicStateSerialized;
	hasRequiredBoundaryCharacteristicStateSerialized = 1;
	Object.defineProperty(BoundaryCharacteristicStateSerialized, "__esModule", { value: true });
	BoundaryCharacteristicStateSerialized.BoundaryCharacteristicStateSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryAssertionStrength_1 = requireBoundaryAssertionStrength();
	const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
	const DesignModels_1 = requireDesignModels();
	const AbsoluteCharacteristicValue_1 = requireAbsoluteCharacteristicValue();
	const ISerializedCircuitState_1 = requireISerializedCircuitState();
	BoundaryCharacteristicStateSerialized.BoundaryCharacteristicStateSerializedSchema = zod_1.default.object({
	    fromSource: zod_1.default.boolean().optional(),
	    assertionStrength: zod_1.default.lazy(() => BoundaryAssertionStrength_1.BoundaryAssertionStrengthSchema),
	    characteristic: zod_1.default.lazy(() => BoundaryCharacteristic_1.BoundaryCharacteristicSchema),
	    circuitState: zod_1.default.lazy(() => ISerializedCircuitState_1.ISerializedCircuitStatePointerSchema),
	    derivedFromBoundaries: zod_1.default.lazy(() => DesignModels_1.BoundaryGlobalIdRefSchema.array()),
	    derivedFromSimulations: zod_1.default
	        .lazy(() => AbsoluteCharacteristicValue_1.SimulationDefinitionSchema)
	        .array()
	        .optional(),
	    derivedFromSpecifications: zod_1.default.string().array().optional(),
	    value: zod_1.default.lazy(() => AbsoluteCharacteristicValue_1.AbsoluteCharacteristicValueSchema),
	    isPartialResolution: zod_1.default.boolean().optional(),
	});
	
	return BoundaryCharacteristicStateSerialized;
}

var hasRequiredINetStats;

function requireINetStats () {
	if (hasRequiredINetStats) return INetStats;
	hasRequiredINetStats = 1;
	Object.defineProperty(INetStats, "__esModule", { value: true });
	INetStats.INetStatsSummarySchema = INetStats.INetStatsSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryCharacteristicStateSummaryExternal_1 = requireBoundaryCharacteristicStateSummaryExternal();
	const BoundaryCharacteristicStateSerialized_1 = requireBoundaryCharacteristicStateSerialized();
	const ISerializedCircuitState_1 = requireISerializedCircuitState();
	INetStats.INetStatsSchema = zod_1.default.object({
	    net: zod_1.default.string(),
	    characteristics: zod_1.default.lazy(() => BoundaryCharacteristicStateSerialized_1.BoundaryCharacteristicStateSerializedSchema).array(),
	    circuitStates: zod_1.default.lazy(() => ISerializedCircuitState_1.SerializedCircuitStateMapSchema).optional(),
	});
	INetStats.INetStatsSummarySchema = zod_1.default.object({
	    net: zod_1.default.string(),
	    characteristics: zod_1.default.lazy(() => BoundaryCharacteristicStateSummaryExternal_1.BoundaryCharacteristicStateSummaryExternalSchema).array(),
	});
	
	return INetStats;
}

var ISimulationStrategy = {};

var ISimulationStratagem = {};

var SpiceDefinitions = {};

var hasRequiredSpiceDefinitions;

function requireSpiceDefinitions () {
	if (hasRequiredSpiceDefinitions) return SpiceDefinitions;
	hasRequiredSpiceDefinitions = 1;
	Object.defineProperty(SpiceDefinitions, "__esModule", { value: true });
	SpiceDefinitions.SpiceModelTypeSchema = SpiceDefinitions.SpiceModelType = SpiceDefinitions.SpiceMeasurementTypeSchema = SpiceDefinitions.SpiceMeasurementType = SpiceDefinitions.SpiceAnalysisTypeSchema = SpiceDefinitions.SpiceAnalysisType = SpiceDefinitions.SpiceLinesSchema = SpiceDefinitions.SpiceLines = SpiceDefinitions.SpiceDotCommandsSchema = SpiceDefinitions.SpiceDotCommands = SpiceDefinitions.SpiceElementTypeSchema = SpiceDefinitions.SpiceElementType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var SpiceElementType;
	(function (SpiceElementType) {
	    SpiceElementType[SpiceElementType["Circuit"] = 0] = "Circuit";
	    SpiceElementType[SpiceElementType["Net"] = 1] = "Net";
	    SpiceElementType[SpiceElementType["Operation"] = 2] = "Operation";
	    SpiceElementType[SpiceElementType["ModelDefinition"] = 3] = "ModelDefinition";
	    SpiceElementType[SpiceElementType["Include"] = 4] = "Include";
	    SpiceElementType["XspiceCodeModel"] = "A";
	    SpiceElementType["BehavioralSource"] = "B";
	    SpiceElementType["Capacitor"] = "C";
	    SpiceElementType["Diode"] = "D";
	    SpiceElementType["VoltageControlledVoltageSource"] = "E";
	    SpiceElementType["NonLinearVoltageSource"] = "E";
	    SpiceElementType["CurrentControlledCurrentSource"] = "F";
	    SpiceElementType["VoltageControlledCurrentSource"] = "G";
	    SpiceElementType["NonLinearCurrentSource"] = "G";
	    SpiceElementType["CurrentControlledVoltageSource"] = "H";
	    SpiceElementType["CurrentSource"] = "I";
	    SpiceElementType["JFET"] = "J";
	    SpiceElementType["CoupledInductors"] = "K";
	    SpiceElementType["Inductor"] = "L";
	    SpiceElementType["MOSFET"] = "M";
	    SpiceElementType["NumericalDeviceForGSS"] = "N";
	    SpiceElementType["LossyTransmissionLine"] = "O";
	    SpiceElementType["CoupledMulticonductorLine"] = "P";
	    SpiceElementType["BJT"] = "Q";
	    SpiceElementType["Resistor"] = "R";
	    SpiceElementType["SwitchVoltageControlled"] = "S";
	    SpiceElementType["LosslessTransmissionLine"] = "T";
	    SpiceElementType["UniformlyDistributedRCLine"] = "U";
	    SpiceElementType["VoltageSource"] = "V";
	    SpiceElementType["SwitchCurrentControlled"] = "W";
	    SpiceElementType["Subcircuit"] = "X";
	    SpiceElementType["SingleLossyTransmissionLine"] = "Y";
	    SpiceElementType["MESFET"] = "Z";
	})(SpiceElementType || (SpiceDefinitions.SpiceElementType = SpiceElementType = {}));
	SpiceDefinitions.SpiceElementTypeSchema = zod_1.default.nativeEnum(SpiceElementType);
	var SpiceDotCommands;
	(function (SpiceDotCommands) {
	    SpiceDotCommands["ACSimulation"] = ".AC";
	    SpiceDotCommands["ControlSectionStart"] = ".CONTROL";
	    SpiceDotCommands["ControlSectionParameters"] = ".CSPARAM";
	    SpiceDotCommands["DCSimulation"] = ".DC";
	    SpiceDotCommands["DistortionAnalysisSimulation"] = ".DISTO";
	    SpiceDotCommands["ConditionalElse"] = ".ELSE";
	    SpiceDotCommands["ConditionalElseIf"] = ".ELSEIF";
	    SpiceDotCommands["NetlistEnd"] = ".END";
	    SpiceDotCommands["ControlSectionEnd"] = ".ENDC";
	    SpiceDotCommands["ConditionalBranchingEnd"] = ".ENDIF";
	    SpiceDotCommands["SubcircuitDefinitionEnd"] = ".ENDS";
	    SpiceDotCommands["FourierAnalysisSimulation"] = ".FOUR";
	    SpiceDotCommands["FunctionDefinition"] = ".FUNC";
	    SpiceDotCommands["GlobalNodeDefinition"] = ".GLOBAL";
	    SpiceDotCommands["SetInitialConditions"] = ".IC";
	    SpiceDotCommands["ConditionIfBranch"] = ".IF";
	    SpiceDotCommands["IncludePart"] = ".INCLUDE";
	    SpiceDotCommands["IncludeLibrary"] = ".LIB";
	    SpiceDotCommands["SimulationMeasurements"] = ".MEAS";
	    SpiceDotCommands["DeviceModelParameters"] = ".MODEL";
	    SpiceDotCommands["SetNodeInitialConditions"] = ".NODESET";
	    SpiceDotCommands["NoiseSimulation"] = ".NOISE";
	    SpiceDotCommands["OperatingPointSimulation"] = ".OP";
	    SpiceDotCommands["SimulatorOptions"] = ".OPTIONS";
	    SpiceDotCommands["ParameterDefinition"] = ".PARAM";
	    SpiceDotCommands["Plot"] = ".PLOT";
	    SpiceDotCommands["Print"] = ".PRINT";
	    SpiceDotCommands["Probe"] = ".Probe";
	    SpiceDotCommands["PeriodicSteadyStateAnalysis"] = ".PSS";
	    SpiceDotCommands["PoleZeroSimulation"] = ".PZ";
	    SpiceDotCommands["Save"] = ".SAVE";
	    SpiceDotCommands["SParameterAnalysis"] = ".SP";
	    SpiceDotCommands["SubcircuitDefinitionStart"] = ".SUBCKT";
	    SpiceDotCommands["CircuitTemperature"] = ".TEMP";
	    SpiceDotCommands["TransferFunctionAnalysis"] = ".TF";
	    SpiceDotCommands["TransientSimulation"] = ".TRAN";
	    SpiceDotCommands["PlotWidth"] = ".WIDTH";
	})(SpiceDotCommands || (SpiceDefinitions.SpiceDotCommands = SpiceDotCommands = {}));
	SpiceDefinitions.SpiceDotCommandsSchema = zod_1.default.nativeEnum(SpiceDotCommands);
	var SpiceLines;
	(function (SpiceLines) {
	    SpiceLines["Title"] = ".TITLE";
	    SpiceLines["End"] = ".END";
	    SpiceLines["ContinuationLine"] = "+";
	    SpiceLines["CommentLine"] = "*";
	})(SpiceLines || (SpiceDefinitions.SpiceLines = SpiceLines = {}));
	SpiceDefinitions.SpiceLinesSchema = zod_1.default.nativeEnum(SpiceLines);
	var SpiceAnalysisType;
	(function (SpiceAnalysisType) {
	    SpiceAnalysisType["DC"] = "dc";
	    SpiceAnalysisType["AC"] = "ac";
	    SpiceAnalysisType["Transient"] = "tran";
	    SpiceAnalysisType["Noise"] = "noise";
	    SpiceAnalysisType["Distortion"] = "disto";
	    SpiceAnalysisType["OperatingPoint"] = "op";
	})(SpiceAnalysisType || (SpiceDefinitions.SpiceAnalysisType = SpiceAnalysisType = {}));
	SpiceDefinitions.SpiceAnalysisTypeSchema = zod_1.default.nativeEnum(SpiceAnalysisType);
	var SpiceMeasurementType;
	(function (SpiceMeasurementType) {
	    SpiceMeasurementType["Voltage"] = "v";
	    SpiceMeasurementType["Current"] = "i";
	    SpiceMeasurementType["VoltageImaginary"] = "vi";
	    SpiceMeasurementType["VoltageReal"] = "vr";
	    SpiceMeasurementType["VoltageMagnitude"] = "vm";
	    SpiceMeasurementType["VoltagePhase"] = "vp";
	    SpiceMeasurementType["VoltageDb"] = "vdb";
	})(SpiceMeasurementType || (SpiceDefinitions.SpiceMeasurementType = SpiceMeasurementType = {}));
	SpiceDefinitions.SpiceMeasurementTypeSchema = zod_1.default.nativeEnum(SpiceMeasurementType);
	var SpiceModelType;
	(function (SpiceModelType) {
	    SpiceModelType["Resistor"] = "R";
	    SpiceModelType["Capacitor"] = "C";
	    SpiceModelType["Inductor"] = "L";
	    SpiceModelType["SwitchVoltageControlled"] = "SW";
	    SpiceModelType["SwitchCurrentControlled"] = "CSW";
	    SpiceModelType["UniformlyDistributedRCLine"] = "URC";
	    SpiceModelType["LossyTransmissionLine"] = "LTRA";
	    SpiceModelType["Diode"] = "D";
	    SpiceModelType["BJT_NPN"] = "NPN";
	    SpiceModelType["BJT_PNP"] = "PNP";
	    SpiceModelType["JFET_N"] = "NJF";
	    SpiceModelType["JFET_P"] = "PJF";
	    SpiceModelType["MOSFET_N"] = "NMOS";
	    SpiceModelType["MOSFET_P"] = "PMOS";
	    SpiceModelType["MESFET_N"] = "NMF";
	    SpiceModelType["MESFET_P"] = "PMF";
	    SpiceModelType["MOSFET_POWER"] = "VDMOS";
	})(SpiceModelType || (SpiceDefinitions.SpiceModelType = SpiceModelType = {}));
	SpiceDefinitions.SpiceModelTypeSchema = zod_1.default.nativeEnum(SpiceModelType);
	
	return SpiceDefinitions;
}

var hasRequiredISimulationStratagem;

function requireISimulationStratagem () {
	if (hasRequiredISimulationStratagem) return ISimulationStratagem;
	hasRequiredISimulationStratagem = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISimulationStratagemSchema = exports.TransientSimulationStratagemSchema = exports.OperatingPointSimulationStratagemSchema = exports.ISimulationStratagemBaseSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SpiceDefinitions_1 = requireSpiceDefinitions();
		exports.ISimulationStratagemBaseSchema = zod_1.default.object({
		    analysisType: SpiceDefinitions_1.SpiceAnalysisTypeSchema,
		    opts: zod_1.default.object({}).passthrough(),
		    description: zod_1.default.string().optional(),
		});
		exports.OperatingPointSimulationStratagemSchema = exports.ISimulationStratagemBaseSchema.extend({
		    analysisType: zod_1.default.literal(SpiceDefinitions_1.SpiceAnalysisType.OperatingPoint),
		    opts: zod_1.default.object({}).passthrough(),
		});
		exports.TransientSimulationStratagemSchema = exports.ISimulationStratagemBaseSchema.extend({
		    analysisType: zod_1.default.literal(SpiceDefinitions_1.SpiceAnalysisType.Transient),
		    opts: zod_1.default.object({}).passthrough(),
		});
		exports.ISimulationStratagemSchema = zod_1.default.discriminatedUnion('analysisType', [
		    exports.OperatingPointSimulationStratagemSchema,
		    exports.TransientSimulationStratagemSchema,
		]);
		
	} (ISimulationStratagem));
	return ISimulationStratagem;
}

var hasRequiredISimulationStrategy;

function requireISimulationStrategy () {
	if (hasRequiredISimulationStrategy) return ISimulationStrategy;
	hasRequiredISimulationStrategy = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISimulationStrategySchema = exports.ISimulationStratagemRecordSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ISimulationStratagem_1 = requireISimulationStratagem();
		const SpiceDefinitions_1 = requireSpiceDefinitions();
		exports.ISimulationStratagemRecordSchema = zod_1.default.object({
		    sourceId: zod_1.default.string(),
		    sourceType: zod_1.default.enum(['inferred', 'specified']),
		    stratagem: zod_1.default.lazy(() => ISimulationStratagem_1.ISimulationStratagemSchema),
		});
		exports.ISimulationStrategySchema = zod_1.default.object({
		    analysisType: SpiceDefinitions_1.SpiceAnalysisTypeSchema,
		    opts: zod_1.default.object({}).passthrough(),
		    stratagems: exports.ISimulationStratagemRecordSchema.array(),
		});
		
	} (ISimulationStrategy));
	return ISimulationStrategy;
}

var ProjectModelNetStats = {};

var hasRequiredProjectModelNetStats;

function requireProjectModelNetStats () {
	if (hasRequiredProjectModelNetStats) return ProjectModelNetStats;
	hasRequiredProjectModelNetStats = 1;
	Object.defineProperty(ProjectModelNetStats, "__esModule", { value: true });
	ProjectModelNetStats.ProjectModelNetStatsSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const INetStats_1 = requireINetStats();
	ProjectModelNetStats.ProjectModelNetStatsSchema = zod_1.default.record(zod_1.default.string(), INetStats_1.INetStatsSummarySchema);
	
	return ProjectModelNetStats;
}

var SpiceSimulationFailureMode = {};

var hasRequiredSpiceSimulationFailureMode;

function requireSpiceSimulationFailureMode () {
	if (hasRequiredSpiceSimulationFailureMode) return SpiceSimulationFailureMode;
	hasRequiredSpiceSimulationFailureMode = 1;
	Object.defineProperty(SpiceSimulationFailureMode, "__esModule", { value: true });
	SpiceSimulationFailureMode.SpiceSimulationFailureMode = void 0;
	var SpiceSimulationFailureMode$1;
	(function (SpiceSimulationFailureMode) {
	    SpiceSimulationFailureMode["CircuitSerializationError"] = "CircuitSerializationError";
	    SpiceSimulationFailureMode["SimulationExecutionError"] = "SimulationExecutionError";
	    SpiceSimulationFailureMode["SimulationParsingError"] = "SimulationParsingError";
	    SpiceSimulationFailureMode["SimulationRunError"] = "SimulationRunError";
	    SpiceSimulationFailureMode["SimulationConvergenceError"] = "SimulationConvergenceError";
	    SpiceSimulationFailureMode["SimulationAbortedError"] = "SimulationAbortedError";
	    SpiceSimulationFailureMode["SimulationTimedOutError"] = "SimulationTimedOutError";
	    SpiceSimulationFailureMode["ResultParseError"] = "ResultParseError";
	    SpiceSimulationFailureMode["ResultCorrelationError"] = "ResultCorrelationError";
	    SpiceSimulationFailureMode["ResultAnalysisError"] = "ResultAnalysisError";
	    SpiceSimulationFailureMode["UnknownSpiceError"] = "UnknownSpiceError";
	})(SpiceSimulationFailureMode$1 || (SpiceSimulationFailureMode.SpiceSimulationFailureMode = SpiceSimulationFailureMode$1 = {}));
	
	return SpiceSimulationFailureMode;
}

var hasRequiredISimulationManifestEntry;

function requireISimulationManifestEntry () {
	if (hasRequiredISimulationManifestEntry) return ISimulationManifestEntry;
	hasRequiredISimulationManifestEntry = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISimulationManifestEntrySerializedSchema = exports.SimulationContentTypeSchema = exports.SimulationContentType = exports.ISpiceSimulationArtifactSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const INetStats_1 = requireINetStats();
		const ISimulationStrategy_1 = requireISimulationStrategy();
		const ProjectModelNetStats_1 = requireProjectModelNetStats();
		const SpiceSimulationFailureMode_1 = requireSpiceSimulationFailureMode();
		const SerializedModeState_1 = requireSerializedModeState();
		exports.ISpiceSimulationArtifactSchema = zod_1.default.object({
		    spiceCircuit: zod_1.default.string().optional(),
		    allStd: zod_1.default.string().optional(),
		    circuitState: zod_1.default.record(zod_1.default.string(), SerializedModeState_1.SerializedModeStateSchema).optional(),
		    strategy: zod_1.default.lazy(() => ISimulationStrategy_1.ISimulationStrategySchema).optional(),
		    failureMode: zod_1.default.nativeEnum(SpiceSimulationFailureMode_1.SpiceSimulationFailureMode).optional(),
		    score: zod_1.default.number().optional(),
		    error: zod_1.default
		        .object({
		        name: zod_1.default.string(),
		        message: zod_1.default.string(),
		        stack: zod_1.default.string().optional(),
		    })
		        .transform((e) => {
		        const result = new Error(e.message);
		        Object.assign(e, result);
		        return result;
		    })
		        .optional(),
		    analysisOpts: zod_1.default.record(zod_1.default.string(), zod_1.default.any()).optional(),
		    netStats: zod_1.default.lazy(() => ProjectModelNetStats_1.ProjectModelNetStatsSchema).optional(),
		    durationMS: zod_1.default.number().optional(),
		});
		var SimulationContentType;
		(function (SimulationContentType) {
		    SimulationContentType["SpiceOperatingPointAnalysisOutput"] = "SpiceOperatingPointAnalysisOutput";
		    SimulationContentType["SpiceTransientAnalysisOutput"] = "SpiceTransientAnalysisOutput";
		    SimulationContentType["BoundaryAnalysisOutput"] = "BoundaryAnalysisOutput";
		    SimulationContentType["SpiceError"] = "SpiceError";
		})(SimulationContentType || (exports.SimulationContentType = SimulationContentType = {}));
		exports.SimulationContentTypeSchema = zod_1.default.nativeEnum(SimulationContentType);
		exports.ISimulationManifestEntrySerializedSchema = zod_1.default.object({
		    resultId: zod_1.default.string(),
		    type: exports.SimulationContentTypeSchema,
		    netStats: zod_1.default
		        .record(zod_1.default.string(), zod_1.default.lazy(() => INetStats_1.INetStatsSchema))
		        .optional(),
		});
		
	} (ISimulationManifestEntry));
	return ISimulationManifestEntry;
}

var SpiceIncludeOpts = {};

var hasRequiredSpiceIncludeOpts;

function requireSpiceIncludeOpts () {
	if (hasRequiredSpiceIncludeOpts) return SpiceIncludeOpts;
	hasRequiredSpiceIncludeOpts = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISubModelOptsSchema = exports.ISigmaSubModelOptsSchema = exports.ISpiceExternalIncludeOptsSchema = exports.ISpiceIncludeOptsSchema = exports.ISubModelOptsBase_Sigma_Schema = exports.ISubModelOptsBase_Spice_Schema = exports.ISubModelOptsBaseSchema = void 0;
		exports.isSubModelSpiceExternalInclude = isSubModelSpiceExternalInclude;
		exports.isSubModelSigmaSubModel = isSubModelSigmaSubModel;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ComponentSpiceModelDefinition_1 = requireComponentSpiceModelDefinition();
		const IModelInfoBase_1 = requireIModelInfoBase();
		exports.ISubModelOptsBaseSchema = zod_1.z.object({
		    type: zod_1.z.enum(['spice', 'sigma']),
		    subModelId: zod_1.z.string(),
		});
		exports.ISubModelOptsBase_Spice_Schema = exports.ISubModelOptsBaseSchema.extend({
		    type: zod_1.z.literal('spice'),
		});
		exports.ISubModelOptsBase_Sigma_Schema = exports.ISubModelOptsBaseSchema.extend({
		    type: zod_1.z.literal('sigma'),
		});
		exports.ISpiceIncludeOptsSchema = exports.ISubModelOptsBase_Spice_Schema.extend({
		    includedSubcircuits: zod_1.z.string().array().optional(),
		    path: zod_1.z.string().optional(),
		    contents: zod_1.z
		        .string()
		        .or(zod_1.z.custom((value) => typeof value === 'function'))
		        .optional(),
		});
		exports.ISpiceExternalIncludeOptsSchema = exports.ISpiceIncludeOptsSchema.extend({
		    baseModel: zod_1.z.string().optional().describe('The ID of the sigma model that this model implements'),
		    modelInfo: ComponentSpiceModelDefinition_1.IComponentSpiceModelDefinitionSubcircuitSchema,
		});
		exports.ISigmaSubModelOptsSchema = exports.ISubModelOptsBase_Sigma_Schema.extend({
		    modelId: zod_1.z.string().describe('The ID of the Sigma model to be included'),
		    modelInfo: (0, IModelInfoBase_1.IModelInfoBaseSchema)(ComponentSpiceModelDefinition_1.IComponentSigmaModelSignalMapItemSchema),
		});
		exports.ISubModelOptsSchema = zod_1.z.discriminatedUnion('type', [
		    exports.ISpiceExternalIncludeOptsSchema,
		    exports.ISigmaSubModelOptsSchema,
		]);
		function isSubModelSpiceExternalInclude(opts) {
		    return (opts?.type === 'spice' ||
		        !opts?.type);
		}
		function isSubModelSigmaSubModel(opts) {
		    return opts?.type === 'sigma';
		}
		
	} (SpiceIncludeOpts));
	return SpiceIncludeOpts;
}

var SpiceModelTestResult = {};

var hasRequiredSpiceModelTestResult;

function requireSpiceModelTestResult () {
	if (hasRequiredSpiceModelTestResult) return SpiceModelTestResult;
	hasRequiredSpiceModelTestResult = 1;
	Object.defineProperty(SpiceModelTestResult, "__esModule", { value: true });
	
	return SpiceModelTestResult;
}

var SpiceOperatingPointAnalysisOpts = {};

var hasRequiredSpiceOperatingPointAnalysisOpts;

function requireSpiceOperatingPointAnalysisOpts () {
	if (hasRequiredSpiceOperatingPointAnalysisOpts) return SpiceOperatingPointAnalysisOpts;
	hasRequiredSpiceOperatingPointAnalysisOpts = 1;
	Object.defineProperty(SpiceOperatingPointAnalysisOpts, "__esModule", { value: true });
	
	return SpiceOperatingPointAnalysisOpts;
}

var SpiceTransientAnalysisOpts = {};

var hasRequiredSpiceTransientAnalysisOpts;

function requireSpiceTransientAnalysisOpts () {
	if (hasRequiredSpiceTransientAnalysisOpts) return SpiceTransientAnalysisOpts;
	hasRequiredSpiceTransientAnalysisOpts = 1;
	Object.defineProperty(SpiceTransientAnalysisOpts, "__esModule", { value: true });
	
	return SpiceTransientAnalysisOpts;
}

var hasRequiredSPICE;

function requireSPICE () {
	if (hasRequiredSPICE) return SPICE;
	hasRequiredSPICE = 1;
	(function (exports) {
		var __createBinding = (SPICE && SPICE.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (SPICE && SPICE.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireISubModelSignalMapItemBase(), exports);
		__exportStar(requireComponentSpiceModelDefinition(), exports);
		__exportStar(requireISimulationManifestEntry(), exports);
		__exportStar(requireISimulationStratagem(), exports);
		__exportStar(requireISimulationStrategy(), exports);
		__exportStar(requireSpiceDefinitions(), exports);
		__exportStar(requireSpiceIncludeOpts(), exports);
		__exportStar(requireSpiceModelTestResult(), exports);
		__exportStar(requireSpiceModelTests(), exports);
		__exportStar(requireSpiceOperatingPointAnalysisOpts(), exports);
		__exportStar(requireSpiceSimulationFailureMode(), exports);
		__exportStar(requireSpiceSourceOpts(), exports);
		__exportStar(requireSpiceTransientAnalysisOpts(), exports);
		
	} (SPICE));
	return SPICE;
}

var BoundaryAnalysisMode = {};

var hasRequiredBoundaryAnalysisMode;

function requireBoundaryAnalysisMode () {
	if (hasRequiredBoundaryAnalysisMode) return BoundaryAnalysisMode;
	hasRequiredBoundaryAnalysisMode = 1;
	Object.defineProperty(BoundaryAnalysisMode, "__esModule", { value: true });
	BoundaryAnalysisMode.BoundaryAnalysisModeSchema = BoundaryAnalysisMode.BoundaryAnalysisMode = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryAnalysisMode$1;
	(function (BoundaryAnalysisMode) {
	    BoundaryAnalysisMode["PossibleValues"] = "possible";
	    BoundaryAnalysisMode["PermissibleValues"] = "permissible";
	})(BoundaryAnalysisMode$1 || (BoundaryAnalysisMode.BoundaryAnalysisMode = BoundaryAnalysisMode$1 = {}));
	BoundaryAnalysisMode.BoundaryAnalysisModeSchema = zod_1.z.nativeEnum(BoundaryAnalysisMode$1);
	
	return BoundaryAnalysisMode;
}

var BoundaryRole = {};

var hasRequiredBoundaryRole;

function requireBoundaryRole () {
	if (hasRequiredBoundaryRole) return BoundaryRole;
	hasRequiredBoundaryRole = 1;
	Object.defineProperty(BoundaryRole, "__esModule", { value: true });
	BoundaryRole.BoundaryRoleSchema = BoundaryRole.BoundaryRole = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryRole$1;
	(function (BoundaryRole) {
	    BoundaryRole["Driver"] = "driver";
	    BoundaryRole["Acceptor"] = "acceptor";
	    BoundaryRole["Constraint"] = "constraint";
	})(BoundaryRole$1 || (BoundaryRole.BoundaryRole = BoundaryRole$1 = {}));
	BoundaryRole.BoundaryRoleSchema = zod_1.z.nativeEnum(BoundaryRole$1);
	
	return BoundaryRole;
}

var CircuitStates = {};

var CharacteristicStateSummaryExternal = {};

var hasRequiredCharacteristicStateSummaryExternal;

function requireCharacteristicStateSummaryExternal () {
	if (hasRequiredCharacteristicStateSummaryExternal) return CharacteristicStateSummaryExternal;
	hasRequiredCharacteristicStateSummaryExternal = 1;
	Object.defineProperty(CharacteristicStateSummaryExternal, "__esModule", { value: true });
	CharacteristicStateSummaryExternal.CharacteristicStateSummaryExternalSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
	const CharacteristicValue_1 = requireCharacteristicValue();
	CharacteristicStateSummaryExternal.CharacteristicStateSummaryExternalSchema = zod_1.default.object({
	    characteristic: zod_1.default.lazy(() => BoundaryCharacteristic_1.BoundaryCharacteristicSchema),
	    value: zod_1.default.lazy(() => CharacteristicValue_1.CharacteristicValueSchema),
	});
	
	return CharacteristicStateSummaryExternal;
}

var IBoundaryConfigurationSerialized = {};

var hasRequiredIBoundaryConfigurationSerialized;

function requireIBoundaryConfigurationSerialized () {
	if (hasRequiredIBoundaryConfigurationSerialized) return IBoundaryConfigurationSerialized;
	hasRequiredIBoundaryConfigurationSerialized = 1;
	Object.defineProperty(IBoundaryConfigurationSerialized, "__esModule", { value: true });
	
	return IBoundaryConfigurationSerialized;
}

var IModeDependency = {};

var hasRequiredIModeDependency;

function requireIModeDependency () {
	if (hasRequiredIModeDependency) return IModeDependency;
	hasRequiredIModeDependency = 1;
	Object.defineProperty(IModeDependency, "__esModule", { value: true });
	IModeDependency.INetStateDependencySchema = IModeDependency.IModeDependencySchema = void 0;
	IModeDependency.isModeDependency = isModeDependency;
	IModeDependency.isNetStateDependency = isNetStateDependency;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AbsoluteCharacteristicValue_1 = requireAbsoluteCharacteristicValue();
	const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
	IModeDependency.IModeDependencySchema = zod_1.default.object({
	    type: zod_1.default.literal('mode'),
	    modeGlobalId: zod_1.default.string(),
	    state: zod_1.default.union([zod_1.default.boolean(), zod_1.default.literal('x')]),
	});
	function isModeDependency(d) {
	    return d.type === 'mode';
	}
	function isNetStateDependency(d) {
	    return d.type === 'net';
	}
	IModeDependency.INetStateDependencySchema = zod_1.default.object({
	    type: zod_1.default.literal('net'),
	    netName: zod_1.default.string(),
	    characteristic: zod_1.default.lazy(() => BoundaryCharacteristic_1.BoundaryCharacteristicSchema),
	    value: zod_1.default.lazy(() => AbsoluteCharacteristicValue_1.AbsoluteCharacteristicValueSchema),
	});
	
	return IModeDependency;
}

var IModeTreeStateVectorSerialized = {};

var hasRequiredIModeTreeStateVectorSerialized;

function requireIModeTreeStateVectorSerialized () {
	if (hasRequiredIModeTreeStateVectorSerialized) return IModeTreeStateVectorSerialized;
	hasRequiredIModeTreeStateVectorSerialized = 1;
	Object.defineProperty(IModeTreeStateVectorSerialized, "__esModule", { value: true });
	IModeTreeStateVectorSerialized.IModeTreeStateVectorSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ComponentFunctionStatesSerialized_1 = requireComponentFunctionStatesSerialized();
	const ISerializedBoundaryConfiguration_1 = requireISerializedBoundaryConfiguration();
	const SerializedModeState_1 = requireSerializedModeState();
	IModeTreeStateVectorSerialized.IModeTreeStateVectorSerializedSchema = zod_1.default.object({
	    modeStates: zod_1.default.record(zod_1.default.string(), SerializedModeState_1.SerializedModeStateSchema),
	    componentFunctionStates: zod_1.default.lazy(() => ComponentFunctionStatesSerialized_1.SerializedComponentFunctionsMapSchema),
	    initialBoundaryMap: zod_1.default
	        .lazy(() => ISerializedBoundaryConfiguration_1.ISerializedBoundaryConfigurationSchema)
	        .array()
	        .optional(),
	});
	
	return IModeTreeStateVectorSerialized;
}

var ISerializedBoundaryScenario = {};

var hasRequiredISerializedBoundaryScenario;

function requireISerializedBoundaryScenario () {
	if (hasRequiredISerializedBoundaryScenario) return ISerializedBoundaryScenario;
	hasRequiredISerializedBoundaryScenario = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ISerializedScenarioAnalysisResultSchema = exports.ISerializedBoundaryScenarioSchema = exports.ISerializedBoundaryRoleAssignmentSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryAnalysisMode_1 = requireBoundaryAnalysisMode();
		const SignalId_1 = requireSignalId();
		const BoundaryCharacteristicStateSerialized_1 = requireBoundaryCharacteristicStateSerialized();
		exports.ISerializedBoundaryRoleAssignmentSchema = zod_1.default.object({
		    boundaryId: zod_1.default.string(),
		    signalId: SignalId_1.SignalIdSchema,
		});
		exports.ISerializedBoundaryScenarioSchema = zod_1.default.object({
		    drivers: exports.ISerializedBoundaryRoleAssignmentSchema.array(),
		    acceptors: exports.ISerializedBoundaryRoleAssignmentSchema.array(),
		    result: BoundaryCharacteristicStateSerialized_1.BoundaryCharacteristicStateSerializedSchema.array(),
		});
		exports.ISerializedScenarioAnalysisResultSchema = zod_1.default.object({
		    mode: BoundaryAnalysisMode_1.BoundaryAnalysisModeSchema,
		    scenarios: exports.ISerializedBoundaryScenarioSchema.array(),
		    bestScenarioPerCharacteristic: zod_1.default.record(zod_1.default.string(), zod_1.default.number()),
		});
		
	} (ISerializedBoundaryScenario));
	return ISerializedBoundaryScenario;
}

var NetStatsWithDependenciesSerialized = {};

var hasRequiredNetStatsWithDependenciesSerialized;

function requireNetStatsWithDependenciesSerialized () {
	if (hasRequiredNetStatsWithDependenciesSerialized) return NetStatsWithDependenciesSerialized;
	hasRequiredNetStatsWithDependenciesSerialized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetStatsWithDependenciesSerializedSchema = exports.NetStatsDependencySchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IModeDependency_1 = requireIModeDependency();
		const INetStats_1 = requireINetStats();
		const ISerializedCircuitState_1 = requireISerializedCircuitState();
		exports.NetStatsDependencySchema = zod_1.default.discriminatedUnion('type', [
		    IModeDependency_1.IModeDependencySchema,
		    IModeDependency_1.INetStateDependencySchema,
		]);
		exports.NetStatsWithDependenciesSerializedSchema = zod_1.default.object({
		    dependencies: exports.NetStatsDependencySchema.array(),
		    resolvesModeStates: IModeDependency_1.IModeDependencySchema.array(),
		    netStats: zod_1.default.record(zod_1.default.string(), INetStats_1.INetStatsSchema),
		    circuitStates: zod_1.default.record(zod_1.default.string(), ISerializedCircuitState_1.ISerializedCircuitStateSchema).optional(),
		});
		
	} (NetStatsWithDependenciesSerialized));
	return NetStatsWithDependenciesSerialized;
}

var RelativeCharacteristicStateValue = {};

var hasRequiredRelativeCharacteristicStateValue;

function requireRelativeCharacteristicStateValue () {
	if (hasRequiredRelativeCharacteristicStateValue) return RelativeCharacteristicStateValue;
	hasRequiredRelativeCharacteristicStateValue = 1;
	Object.defineProperty(RelativeCharacteristicStateValue, "__esModule", { value: true });
	RelativeCharacteristicStateValue.RelativeCharacteristicStateValueSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AbsoluteCharacteristicValue_1 = requireAbsoluteCharacteristicValue();
	RelativeCharacteristicStateValue.RelativeCharacteristicStateValueSchema = AbsoluteCharacteristicValue_1.AbsoluteCharacteristicValueSchema.extend({
	    relativeToNet: zod_1.default.string().optional(),
	});
	
	return RelativeCharacteristicStateValue;
}

var hasRequiredCircuitStates;

function requireCircuitStates () {
	if (hasRequiredCircuitStates) return CircuitStates;
	hasRequiredCircuitStates = 1;
	(function (exports) {
		var __createBinding = (CircuitStates && CircuitStates.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (CircuitStates && CircuitStates.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAbsoluteCharacteristicValue(), exports);
		__exportStar(requireAbsoluteCharacteristicValueBase(), exports);
		__exportStar(requireBoundaryCharacteristicStateSummaryExternal(), exports);
		__exportStar(requireCharacteristicStateSummaryExternal(), exports);
		__exportStar(requireCharacteristicValue(), exports);
		__exportStar(requireComponentFunctionStatesSerialized(), exports);
		__exportStar(requireIBoundaryConfigurationSerialized(), exports);
		__exportStar(requireIModeDependency(), exports);
		__exportStar(requireIModeTreeStateVectorSerialized(), exports);
		__exportStar(requireINetStats(), exports);
		__exportStar(requireISerializedBoundaryConfiguration(), exports);
		__exportStar(requireISerializedBoundaryScenario(), exports);
		__exportStar(requireISerializedCircuitState(), exports);
		__exportStar(requireISerializedCircuitState(), exports);
		__exportStar(requireISerializedProvenanceNode(), exports);
		__exportStar(requireNetStatsWithDependenciesSerialized(), exports);
		__exportStar(requireRelativeCharacteristicStateValue(), exports);
		__exportStar(requireSerializedBoundaryState(), exports);
		__exportStar(requireSerializedModeState(), exports);
		
	} (CircuitStates));
	return CircuitStates;
}

var DesignConfiguration = {};

var hasRequiredDesignConfiguration;

function requireDesignConfiguration () {
	if (hasRequiredDesignConfiguration) return DesignConfiguration;
	hasRequiredDesignConfiguration = 1;
	Object.defineProperty(DesignConfiguration, "__esModule", { value: true });
	DesignConfiguration.VirtualModuleConfigurationType = void 0;
	DesignConfiguration.generateDnpConfiguration = generateDnpConfiguration;
	var VirtualModuleConfigurationType;
	(function (VirtualModuleConfigurationType) {
	    VirtualModuleConfigurationType["ElectricalOverride"] = "electricalOverride";
	    VirtualModuleConfigurationType["CustomComponent"] = "customComponent";
	})(VirtualModuleConfigurationType || (DesignConfiguration.VirtualModuleConfigurationType = VirtualModuleConfigurationType = {}));
	function generateDnpConfiguration(project) {
	    const designConfig = {
	        modules: {},
	    };
	    for (const module of project.modules) {
	        if (module.isDnp) {
	            designConfig.modules[module.name] = {
	                name: module.name,
	                isDnp: true,
	            };
	        }
	    }
	    return designConfig;
	}
	
	return DesignConfiguration;
}

var FunctionalModels = {};

var ComponentFunctionBusRole = {};

var hasRequiredComponentFunctionBusRole;

function requireComponentFunctionBusRole () {
	if (hasRequiredComponentFunctionBusRole) return ComponentFunctionBusRole;
	hasRequiredComponentFunctionBusRole = 1;
	Object.defineProperty(ComponentFunctionBusRole, "__esModule", { value: true });
	ComponentFunctionBusRole.ComponentFunctionBusRoleSchema = ComponentFunctionBusRole.ComponentFunctionBusRole = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var ComponentFunctionBusRole$1;
	(function (ComponentFunctionBusRole) {
	    ComponentFunctionBusRole["Controller"] = "controller";
	    ComponentFunctionBusRole["Peripheral"] = "peripheral";
	})(ComponentFunctionBusRole$1 || (ComponentFunctionBusRole.ComponentFunctionBusRole = ComponentFunctionBusRole$1 = {}));
	ComponentFunctionBusRole.ComponentFunctionBusRoleSchema = zod_1.z.nativeEnum(ComponentFunctionBusRole$1);
	
	return ComponentFunctionBusRole;
}

var ComponentFunctionBusType = {};

var hasRequiredComponentFunctionBusType;

function requireComponentFunctionBusType () {
	if (hasRequiredComponentFunctionBusType) return ComponentFunctionBusType;
	hasRequiredComponentFunctionBusType = 1;
	Object.defineProperty(ComponentFunctionBusType, "__esModule", { value: true });
	ComponentFunctionBusType.ComponentFunctionBusTypeSchema = ComponentFunctionBusType.ComponentFunctionBusType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var ComponentFunctionBusType$1;
	(function (ComponentFunctionBusType) {
	    ComponentFunctionBusType["Common"] = "common";
	    ComponentFunctionBusType["SCSP"] = "scsp";
	    ComponentFunctionBusType["SCMP"] = "scmp";
	    ComponentFunctionBusType["MCSP"] = "mcsp";
	    ComponentFunctionBusType["MCMP"] = "mcmp";
	})(ComponentFunctionBusType$1 || (ComponentFunctionBusType.ComponentFunctionBusType = ComponentFunctionBusType$1 = {}));
	ComponentFunctionBusType.ComponentFunctionBusTypeSchema = zod_1.z.nativeEnum(ComponentFunctionBusType$1);
	
	return ComponentFunctionBusType;
}

var ComponentFunctionType = {};

var hasRequiredComponentFunctionType;

function requireComponentFunctionType () {
	if (hasRequiredComponentFunctionType) return ComponentFunctionType;
	hasRequiredComponentFunctionType = 1;
	Object.defineProperty(ComponentFunctionType, "__esModule", { value: true });
	ComponentFunctionType.ComponentFunctionTypeSchema = ComponentFunctionType.ComponentFunctionType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var ComponentFunctionType$1;
	(function (ComponentFunctionType) {
	    ComponentFunctionType["Bus"] = "bus";
	    ComponentFunctionType["Custom"] = "custom";
	})(ComponentFunctionType$1 || (ComponentFunctionType.ComponentFunctionType = ComponentFunctionType$1 = {}));
	ComponentFunctionType.ComponentFunctionTypeSchema = zod_1.z.nativeEnum(ComponentFunctionType$1);
	
	return ComponentFunctionType;
}

var IComponentFunction = {};

var IComponentFunctionSignal = {};

var IComponentFunctionSignalBase = {};

var SignalDirection = {};

var hasRequiredSignalDirection;

function requireSignalDirection () {
	if (hasRequiredSignalDirection) return SignalDirection;
	hasRequiredSignalDirection = 1;
	Object.defineProperty(SignalDirection, "__esModule", { value: true });
	SignalDirection.SignalDirectionSchema = SignalDirection.SignalDirection = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var SignalDirection$1;
	(function (SignalDirection) {
	    SignalDirection["BiDirectionalIO"] = "biio";
	    SignalDirection["Input"] = "input";
	    SignalDirection["Output"] = "output";
	})(SignalDirection$1 || (SignalDirection.SignalDirection = SignalDirection$1 = {}));
	SignalDirection.SignalDirectionSchema = zod_1.z.nativeEnum(SignalDirection$1);
	
	return SignalDirection;
}

var hasRequiredIComponentFunctionSignalBase;

function requireIComponentFunctionSignalBase () {
	if (hasRequiredIComponentFunctionSignalBase) return IComponentFunctionSignalBase;
	hasRequiredIComponentFunctionSignalBase = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IComponentFunctionSignalBaseSchema = exports.IComponentFunctionSignalChildBusMappingSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ComponentFunctionBusType_1 = requireComponentFunctionBusType();
		const SignalDirection_1 = requireSignalDirection();
		exports.IComponentFunctionSignalChildBusMappingSchema = zod_1.z.object({
		    childBusName: zod_1.z.string(),
		    childSignalName: zod_1.z.string(),
		});
		exports.IComponentFunctionSignalBaseSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    optional: zod_1.z.boolean().optional(),
		    isMultiAssignment: zod_1.z.boolean().optional(),
		    busType: ComponentFunctionBusType_1.ComponentFunctionBusTypeSchema.optional(),
		    busRole: zod_1.z.string().optional(),
		    signalTypes: zod_1.z.lazy(() => SignalDirection_1.SignalDirectionSchema.array().optional()),
		    childBusMapping: exports.IComponentFunctionSignalChildBusMappingSchema.optional(),
		});
		
	} (IComponentFunctionSignalBase));
	return IComponentFunctionSignalBase;
}

var IComponentFunctionSignalAttributes = {};

var Signal = {};

var SignalBlockInfo = {};

var hasRequiredSignalBlockInfo;

function requireSignalBlockInfo () {
	if (hasRequiredSignalBlockInfo) return SignalBlockInfo;
	hasRequiredSignalBlockInfo = 1;
	Object.defineProperty(SignalBlockInfo, "__esModule", { value: true });
	SignalBlockInfo.SignalBlockInfoSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	SignalBlockInfo.SignalBlockInfoSchema = zod_1.z.object({
	    name: zod_1.z.string().optional(),
	    blockPattern: zod_1.z.string().optional(),
	    index: zod_1.z.number(),
	});
	
	return SignalBlockInfo;
}

var SignalAttributes = {};

var SignalTypeDeprecated = {};

var hasRequiredSignalTypeDeprecated;

function requireSignalTypeDeprecated () {
	if (hasRequiredSignalTypeDeprecated) return SignalTypeDeprecated;
	hasRequiredSignalTypeDeprecated = 1;
	Object.defineProperty(SignalTypeDeprecated, "__esModule", { value: true });
	SignalTypeDeprecated.SignalType = SignalTypeDeprecated.SignalTypeDeprecated = void 0;
	var SignalTypeDeprecated$1;
	(function (SignalTypeDeprecated) {
	    SignalTypeDeprecated["Power"] = "power";
	    SignalTypeDeprecated["PowerGND"] = "power.gnd";
	    SignalTypeDeprecated["PowerVCC"] = "power.vcc";
	    SignalTypeDeprecated["PowerVEE"] = "power.vee";
	    SignalTypeDeprecated["PowerAnalogGND"] = "power.agnd";
	    SignalTypeDeprecated["PowerAnalogVCC"] = "power.avcc";
	})(SignalTypeDeprecated$1 || (SignalTypeDeprecated.SignalTypeDeprecated = SignalTypeDeprecated$1 = {}));
	var SignalType;
	(function (SignalType) {
	    SignalType["Power"] = "power";
	    SignalType["Ground"] = "ground";
	    SignalType["Analog"] = "analog";
	    SignalType["Digital"] = "digital";
	    SignalType["NoConnect"] = "nc";
	})(SignalType || (SignalTypeDeprecated.SignalType = SignalType = {}));
	
	return SignalTypeDeprecated;
}

var hasRequiredSignalAttributes;

function requireSignalAttributes () {
	if (hasRequiredSignalAttributes) return SignalAttributes;
	hasRequiredSignalAttributes = 1;
	Object.defineProperty(SignalAttributes, "__esModule", { value: true });
	SignalAttributes.SignalAttributesSchema = void 0;
	SignalAttributes.resolveSignalAttributes = resolveSignalAttributes;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SignalTypeDeprecated_1 = requireSignalTypeDeprecated();
	SignalAttributes.SignalAttributesSchema = zod_1.z.object({
	    power: zod_1.z.boolean().optional(),
	    ground: zod_1.z.boolean().optional(),
	    analog: zod_1.z.boolean().optional(),
	    digital: zod_1.z.boolean().optional(),
	    exposedPad: zod_1.z.boolean().optional(),
	    noConnect: zod_1.z.boolean().optional(),
	    activeLow: zod_1.z.boolean().optional(),
	});
	function resolveSignalAttributes(signal) {
	    return {
	        power: (signal.attributes?.power ?? signal.signalType === SignalTypeDeprecated_1.SignalType.Power) || false,
	        ground: (signal.attributes?.ground ?? signal.signalType === SignalTypeDeprecated_1.SignalType.Ground) || false,
	        analog: (signal.attributes?.analog ?? signal.analog ?? signal.signalType === SignalTypeDeprecated_1.SignalType.Analog) || false,
	        digital: (signal.attributes?.digital ?? signal.signalType === SignalTypeDeprecated_1.SignalType.Digital) || false,
	        noConnect: (signal.attributes?.noConnect ?? signal.signalType === SignalTypeDeprecated_1.SignalType.NoConnect) || false,
	        exposedPad: signal.attributes?.exposedPad ?? false,
	        activeLow: signal.attributes?.activeLow ?? false,
	    };
	}
	
	return SignalAttributes;
}

var DifferentialPair = {};

var hasRequiredDifferentialPair$1;

function requireDifferentialPair$1 () {
	if (hasRequiredDifferentialPair$1) return DifferentialPair;
	hasRequiredDifferentialPair$1 = 1;
	Object.defineProperty(DifferentialPair, "__esModule", { value: true });
	DifferentialPair.DifferentialPairSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const NumericalBoundary_1 = requireNumericalBoundary();
	DifferentialPair.DifferentialPairSchema = zod_1.z.object({
	    pairName: zod_1.z.string(),
	    positiveId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
	    negativeId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
	    differentialVoltageSwing: zod_1.z
	        .union([NumericalBoundary_1.NumericalBoundaryMinimumSchema, NumericalBoundary_1.NumericalBoundaryMaximumSchema, NumericalBoundary_1.NumericalBoundaryRangeSchema])
	        .optional(),
	});
	
	return DifferentialPair;
}

var ILogger = {};

var hasRequiredILogger;

function requireILogger () {
	if (hasRequiredILogger) return ILogger;
	hasRequiredILogger = 1;
	Object.defineProperty(ILogger, "__esModule", { value: true });
	ILogger.generateCompatibleConsoleLogger = generateCompatibleConsoleLogger;
	function generateCompatibleConsoleLogger() {
	    const logger = {
	        debug: (message, ...meta) => console.debug(message, ...meta),
	        verbose: (message, ...meta) => console.log(message, ...meta),
	        info: (message, ...meta) => console.log(message, ...meta),
	        warn: (message, ...meta) => console.warn(message, ...meta),
	        error: (message, ...meta) => console.error(message, ...meta),
	    };
	    return logger;
	}
	
	return ILogger;
}

var SignalFloatingBehavior = {};

var hasRequiredSignalFloatingBehavior;

function requireSignalFloatingBehavior () {
	if (hasRequiredSignalFloatingBehavior) return SignalFloatingBehavior;
	hasRequiredSignalFloatingBehavior = 1;
	Object.defineProperty(SignalFloatingBehavior, "__esModule", { value: true });
	SignalFloatingBehavior.SignalFloatingBehaviorSchema = SignalFloatingBehavior.SignalFloatingBehavior = void 0;
	const z = /*@__PURE__*/ requireZod();
	var SignalFloatingBehavior$1;
	(function (SignalFloatingBehavior) {
	    SignalFloatingBehavior["Always"] = "always";
	    SignalFloatingBehavior["Sometimes"] = "sometimes";
	    SignalFloatingBehavior["Never"] = "never";
	    SignalFloatingBehavior["Unspecified"] = "unspecified";
	    SignalFloatingBehavior["Conditional"] = "conditional";
	})(SignalFloatingBehavior$1 || (SignalFloatingBehavior.SignalFloatingBehavior = SignalFloatingBehavior$1 = {}));
	SignalFloatingBehavior.SignalFloatingBehaviorSchema = z.nativeEnum(SignalFloatingBehavior$1);
	
	return SignalFloatingBehavior;
}

var hasRequiredSignal;

function requireSignal () {
	if (hasRequiredSignal) return Signal;
	hasRequiredSignal = 1;
	Object.defineProperty(Signal, "__esModule", { value: true });
	Signal.SignalSchema = void 0;
	Signal.findSignalIdBySignalName = findSignalIdBySignalName;
	Signal.findSignalNameBySignalId = findSignalNameBySignalId;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SignalBlockInfo_1 = requireSignalBlockInfo();
	const metadata_1 = requireMetadata();
	const SignalAttributes_1 = requireSignalAttributes();
	const SignalDirection_1 = requireSignalDirection();
	const SignalDriveMode_1 = requireSignalDriveMode();
	const SignalId_1 = requireSignalId();
	const DifferentialPair_1 = requireDifferentialPair$1();
	const ILogger_1 = requireILogger();
	const SignalFloatingBehavior_1 = requireSignalFloatingBehavior();
	const IExternalComponent_1 = requireIExternalComponent();
	Signal.SignalSchema = zod_1.z.object({
	    id: SignalId_1.SignalIdSchema,
	    name: zod_1.z.string(),
	    blockInfo: zod_1.z.lazy(() => SignalBlockInfo_1.SignalBlockInfoSchema).optional(),
	    padRefs: zod_1.z.string().array(),
	    pinRefs: zod_1.z.union([zod_1.z.string(), zod_1.z.null()]).array(),
	    signalDirection: zod_1.z.lazy(() => SignalDirection_1.SignalDirectionSchema).optional(),
	    attributes: zod_1.z.lazy(() => SignalAttributes_1.SignalAttributesSchema).optional(),
	    description: zod_1.z.string().optional(),
	    modelSignal: SignalId_1.SignalIdSchema.optional(),
	    metadata: metadata_1.MetadataSchema.optional(),
	    diffPair: DifferentialPair_1.DifferentialPairSchema.optional(),
	    supportedDriveModes: SignalDriveMode_1.SignalDriveModeDefinitionSchema.array().optional(),
	    floatingBehavior: zod_1.z.nativeEnum(SignalFloatingBehavior_1.SignalFloatingBehavior).optional(),
	    ties: IExternalComponent_1.IExternalComponentSchema.array().optional(),
	    isCadstromInternal: zod_1.z.boolean().optional(),
	});
	function findSignalIdBySignalName(signalName, signals, logger = (0, ILogger_1.generateCompatibleConsoleLogger)()) {
	    const matchingSignals = signals.filter((signal) => signal.name === signalName);
	    if (matchingSignals.length === 0) {
	        logger.warn('FindSignalIdBySignalName_MatchNotFound', {
	            signalName,
	            availableSignalNames: signals.map((s) => s.name),
	        });
	        return null;
	    }
	    if (matchingSignals.length > 1) {
	        logger.warn('FindSignalIdBySignalName_DuplicateSignalName', {
	            signalName,
	            matchingSignalIds: matchingSignals.map((s) => s.id),
	            message: 'Multiple signals found with same name, using first occurrence',
	        });
	    }
	    return String(matchingSignals[0].id);
	}
	function findSignalNameBySignalId(signalId, signals, logger = (0, ILogger_1.generateCompatibleConsoleLogger)()) {
	    const matchingSignal = signals.find((signal) => String(signal.id) === String(signalId));
	    if (!matchingSignal) {
	        logger.warn('FindSignalNameBySignalId_MatchNotFound', {
	            signalId,
	            availableSignalIds: signals.map((s) => s.id),
	        });
	        return null;
	    }
	    return matchingSignal.name;
	}
	
	return Signal;
}

var hasRequiredIComponentFunctionSignalAttributes;

function requireIComponentFunctionSignalAttributes () {
	if (hasRequiredIComponentFunctionSignalAttributes) return IComponentFunctionSignalAttributes;
	hasRequiredIComponentFunctionSignalAttributes = 1;
	Object.defineProperty(IComponentFunctionSignalAttributes, "__esModule", { value: true });
	IComponentFunctionSignalAttributes.IComponentFunctionSignalAttributesSchema = void 0;
	const Signal_1 = requireSignal();
	IComponentFunctionSignalAttributes.IComponentFunctionSignalAttributesSchema = Signal_1.SignalSchema.pick({
	    signalDirection: true,
	    attributes: true,
	    diffPair: true,
	    supportedDriveModes: true,
	    floatingBehavior: true,
	    ties: true,
	});
	
	return IComponentFunctionSignalAttributes;
}

var hasRequiredIComponentFunctionSignal;

function requireIComponentFunctionSignal () {
	if (hasRequiredIComponentFunctionSignal) return IComponentFunctionSignal;
	hasRequiredIComponentFunctionSignal = 1;
	Object.defineProperty(IComponentFunctionSignal, "__esModule", { value: true });
	IComponentFunctionSignal.IComponentFunctionSignalSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IComponentFunctionSignalBase_1 = requireIComponentFunctionSignalBase();
	const RelativeSignal_1 = requireRelativeSignal();
	const IComponentFunctionSignalAttributes_1 = requireIComponentFunctionSignalAttributes();
	IComponentFunctionSignal.IComponentFunctionSignalSchema = IComponentFunctionSignalBase_1.IComponentFunctionSignalBaseSchema.extend({
	    signals: RelativeSignal_1.RelativeSignalSchema.array(),
	    description: zod_1.z.string().optional(),
	    classSignalDescription: zod_1.z.string().optional(),
	    alternateNames: zod_1.z.string().array().optional(),
	    maxAssignments: zod_1.z.number().optional(),
	    physicalAttributes: IComponentFunctionSignalAttributes_1.IComponentFunctionSignalAttributesSchema.optional(),
	});
	
	return IComponentFunctionSignal;
}

var IComponentFunctionAbstract = {};

var IComponentFunctionBoundary = {};

var IComponentFunctionBoundaryDefinition = {};

var hasRequiredIComponentFunctionBoundaryDefinition;

function requireIComponentFunctionBoundaryDefinition () {
	if (hasRequiredIComponentFunctionBoundaryDefinition) return IComponentFunctionBoundaryDefinition;
	hasRequiredIComponentFunctionBoundaryDefinition = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IComponentFunctionBoundaryDefinitionSchema = exports.IComponentFunctionBoundaryDefinitionSchema_ConnectionDefinition = exports.IComponentFunctionBoundaryDefinitionSchema_NumericalDefinition = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const INumericalBoundaryDefinition_1 = requireINumericalBoundaryDefinition();
		const IConnectionBoundaryDefinition_1 = requireIConnectionBoundaryDefinition();
		const IBoundaryBase_1 = requireIBoundaryBase();
		exports.IComponentFunctionBoundaryDefinitionSchema_NumericalDefinition = INumericalBoundaryDefinition_1.INumericalBoundaryDefinitionSchema.merge(IBoundaryBase_1.IBoundaryBaseSchema.omit({ target: true, relative_to: true })).extend({
		    target: zod_1.z.union([
		        IBoundaryBase_1.IBoundaryBaseSchema.shape.target,
		        zod_1.z.object({
		            signalSlots: zod_1.z.string().array(),
		        }),
		    ]),
		    relative_to: zod_1.z
		        .union([
		        IBoundaryBase_1.IBoundaryBaseSchema.shape.relative_to,
		        zod_1.z.object({
		            signalSlots: zod_1.z.string().array(),
		        }),
		    ])
		        .optional(),
		});
		exports.IComponentFunctionBoundaryDefinitionSchema_ConnectionDefinition = IConnectionBoundaryDefinition_1.IConnectionBoundaryDefinitionSchema.merge(IBoundaryBase_1.IBoundaryBaseSchema.omit({ target: true, relative_to: true })).extend({
		    target: zod_1.z.union([
		        IBoundaryBase_1.IBoundaryBaseSchema.shape.target,
		        zod_1.z.object({
		            signalSlots: zod_1.z.string().array(),
		        }),
		    ]),
		    relative_to: zod_1.z
		        .union([
		        IBoundaryBase_1.IBoundaryBaseSchema.shape.relative_to,
		        zod_1.z.object({
		            signalSlots: zod_1.z.string().array(),
		        }),
		    ])
		        .optional(),
		});
		exports.IComponentFunctionBoundaryDefinitionSchema = zod_1.z.discriminatedUnion('type', [
		    exports.IComponentFunctionBoundaryDefinitionSchema_NumericalDefinition,
		    exports.IComponentFunctionBoundaryDefinitionSchema_ConnectionDefinition,
		]);
		
	} (IComponentFunctionBoundaryDefinition));
	return IComponentFunctionBoundaryDefinition;
}

var hasRequiredIComponentFunctionBoundary;

function requireIComponentFunctionBoundary () {
	if (hasRequiredIComponentFunctionBoundary) return IComponentFunctionBoundary;
	hasRequiredIComponentFunctionBoundary = 1;
	Object.defineProperty(IComponentFunctionBoundary, "__esModule", { value: true });
	IComponentFunctionBoundary.IComponentFunctionBoundarySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IComponentFunctionBoundaryDefinition_1 = requireIComponentFunctionBoundaryDefinition();
	const IModeCondition_1 = requireIModeCondition();
	IComponentFunctionBoundary.IComponentFunctionBoundarySchema = zod_1.z.object({
	    id: zod_1.z.string(),
	    globlal: zod_1.z.boolean().optional(),
	    conditions: zod_1.z.lazy(() => IModeCondition_1.IModeConditionSchema.array().optional()),
	    boundary: zod_1.z.lazy(() => IComponentFunctionBoundaryDefinition_1.IComponentFunctionBoundaryDefinitionSchema),
	});
	
	return IComponentFunctionBoundary;
}

var IFunctionCondition = {};

var IExternalComponentCondition = {};

var hasRequiredIExternalComponentCondition;

function requireIExternalComponentCondition () {
	if (hasRequiredIExternalComponentCondition) return IExternalComponentCondition;
	hasRequiredIExternalComponentCondition = 1;
	Object.defineProperty(IExternalComponentCondition, "__esModule", { value: true });
	IExternalComponentCondition.IExternalComponentConditionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IExternalComponent_1 = requireIExternalComponent();
	IExternalComponentCondition.IExternalComponentConditionSchema = zod_1.z.object({
	    name: zod_1.z.string(),
	    description: zod_1.z.string().optional(),
	    functionSignal: zod_1.z.string(),
	    components: zod_1.z.lazy(() => IExternalComponent_1.IExternalComponentSchema).array(),
	});
	
	return IExternalComponentCondition;
}

var hasRequiredIFunctionCondition;

function requireIFunctionCondition () {
	if (hasRequiredIFunctionCondition) return IFunctionCondition;
	hasRequiredIFunctionCondition = 1;
	Object.defineProperty(IFunctionCondition, "__esModule", { value: true });
	IFunctionCondition.IFunctionConditionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const FunctionReferenceCondition_1 = requireFunctionReferenceCondition();
	const IExternalComponentCondition_1 = requireIExternalComponentCondition();
	const IModeCondition_1 = requireIModeCondition();
	IFunctionCondition.IFunctionConditionSchema = zod_1.z.lazy(() => zod_1.z.union([IModeCondition_1.IModeConditionSchema, FunctionReferenceCondition_1.FunctionReferenceConditionSchema, IExternalComponentCondition_1.IExternalComponentConditionSchema]));
	
	return IFunctionCondition;
}

var IComponentFunctionClassParameters = {};

var hasRequiredIComponentFunctionClassParameters;

function requireIComponentFunctionClassParameters () {
	if (hasRequiredIComponentFunctionClassParameters) return IComponentFunctionClassParameters;
	hasRequiredIComponentFunctionClassParameters = 1;
	Object.defineProperty(IComponentFunctionClassParameters, "__esModule", { value: true });
	IComponentFunctionClassParameters.IComponentFunctionClassParametersSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	IComponentFunctionClassParameters.IComponentFunctionClassParametersSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.union([
	    zod_1.z.string(),
	    zod_1.z.boolean(),
	    zod_1.z.number(),
	    zod_1.z.any().refine((v) => typeof v === 'object' || typeof v === 'function', {
	        message: 'Expected an object',
	    }),
	    zod_1.z.undefined(),
	]));
	
	return IComponentFunctionClassParameters;
}

var hasRequiredIComponentFunctionAbstract;

function requireIComponentFunctionAbstract () {
	if (hasRequiredIComponentFunctionAbstract) return IComponentFunctionAbstract;
	hasRequiredIComponentFunctionAbstract = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IComponentFunctionAbstractSchema = exports.IComponentFunctionChildBusSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ComponentFunctionBusRole_1 = requireComponentFunctionBusRole();
		const IComponentFunctionSignalBase_1 = requireIComponentFunctionSignalBase();
		const IComponentFunctionBoundary_1 = requireIComponentFunctionBoundary();
		const IFunctionCondition_1 = requireIFunctionCondition();
		const IComponentFunctionClassParameters_1 = requireIComponentFunctionClassParameters();
		exports.IComponentFunctionChildBusSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    childFunctionClass: zod_1.z.string(),
		    classParameters: IComponentFunctionClassParameters_1.IComponentFunctionClassParametersSchema.optional(),
		});
		exports.IComponentFunctionAbstractSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    functionClass: zod_1.z.string().optional(),
		    busRole: zod_1.z.nativeEnum(ComponentFunctionBusRole_1.ComponentFunctionBusRole).optional(),
		    mustBeAssigned: zod_1.z.boolean().optional(),
		    classParameters: IComponentFunctionClassParameters_1.IComponentFunctionClassParametersSchema.optional(),
		    conditions: zod_1.z
		        .lazy(() => IFunctionCondition_1.IFunctionConditionSchema)
		        .array()
		        .optional(),
		    functionSignals: zod_1.z.lazy(() => IComponentFunctionSignalBase_1.IComponentFunctionSignalBaseSchema).array(),
		    resourceConsumed: zod_1.z.string().optional(),
		    boundaries: zod_1.z
		        .lazy(() => IComponentFunctionBoundary_1.IComponentFunctionBoundarySchema)
		        .array()
		        .optional(),
		    childBuses: exports.IComponentFunctionChildBusSchema.array().optional(),
		});
		
	} (IComponentFunctionAbstract));
	return IComponentFunctionAbstract;
}

var hasRequiredIComponentFunction;

function requireIComponentFunction () {
	if (hasRequiredIComponentFunction) return IComponentFunction;
	hasRequiredIComponentFunction = 1;
	Object.defineProperty(IComponentFunction, "__esModule", { value: true });
	IComponentFunction.IComponentFunctionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IComponentFunctionSignal_1 = requireIComponentFunctionSignal();
	const IComponentFunctionAbstract_1 = requireIComponentFunctionAbstract();
	IComponentFunction.IComponentFunctionSchema = IComponentFunctionAbstract_1.IComponentFunctionAbstractSchema.extend({
	    functionSignals: zod_1.z.lazy(() => IComponentFunctionSignal_1.IComponentFunctionSignalSchema).array(),
	});
	
	return IComponentFunction;
}

var IComponentFunctionAssignment = {};

var IExternalComponentAssignment = {};

var hasRequiredIExternalComponentAssignment;

function requireIExternalComponentAssignment () {
	if (hasRequiredIExternalComponentAssignment) return IExternalComponentAssignment;
	hasRequiredIExternalComponentAssignment = 1;
	Object.defineProperty(IExternalComponentAssignment, "__esModule", { value: true });
	IExternalComponentAssignment.IExternalComponentAssignmentSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const zod_field_presence_1 = requireZodFieldPresence();
	IExternalComponentAssignment.IExternalComponentAssignmentSchema = zod_1.z.object({
	    assignment: zod_1.z.string(),
	    traversals: (0, zod_field_presence_1.allowingAbsentKey)(zod_1.z.custom()),
	});
	
	return IExternalComponentAssignment;
}

var hasRequiredIComponentFunctionAssignment;

function requireIComponentFunctionAssignment () {
	if (hasRequiredIComponentFunctionAssignment) return IComponentFunctionAssignment;
	hasRequiredIComponentFunctionAssignment = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IComponentFunctionAssignmentSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const Signal_1 = requireSignal();
		const FunctionalAddressing_1 = requireFunctionalAddressing();
		const IExternalComponentAssignment_1 = requireIExternalComponentAssignment();
		const IComponentFunctionSignalAssignment_1 = requireIComponentFunctionSignalAssignment();
		const SignalAssignmentsUnionSchema = zod_1.z.union([
		    Signal_1.SignalSchema.pick({ id: true }).passthrough().array(),
		    IComponentFunctionSignalAssignment_1.IComponentFunctionSignalAssignmentSchema.array(),
		]);
		function transformSignalAssignments(value) {
		    if (value.length === 0) {
		        return [];
		    }
		    const firstElement = value[0];
		    if ('componentSignalId' in firstElement) {
		        return value;
		    }
		    return value.map((signal) => ({
		        componentSignalId: signal.id,
		    }));
		}
		exports.IComponentFunctionAssignmentSchema = zod_1.z.lazy(() => zod_1.z.object({
		    function: zod_1.z.string(),
		    functionClass: zod_1.z.string().optional(),
		    isUnassigned: zod_1.z.boolean().optional(),
		    signalAssignments: zod_1.z.record(zod_1.z.string(), SignalAssignmentsUnionSchema.transform(transformSignalAssignments)),
		    externalComponentAssignments: zod_1.z.record(zod_1.z.string(), zod_1.z.record(zod_1.z.string(), zod_1.z.lazy(() => IExternalComponentAssignment_1.IExternalComponentAssignmentSchema).array())),
		    addressableComponents: zod_1.z
		        .lazy(() => FunctionalAddressing_1.ControllerBusAddressSchema)
		        .array()
		        .optional(),
		    externalConnectionAssignments: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    childBusAssignments: zod_1.z
		        .record(zod_1.z.string(), zod_1.z.lazy(() => exports.IComponentFunctionAssignmentSchema))
		        .optional(),
		}));
		
	} (IComponentFunctionAssignment));
	return IComponentFunctionAssignment;
}

var NetFunctionalDescription = {};

var hasRequiredNetFunctionalDescription;

function requireNetFunctionalDescription () {
	if (hasRequiredNetFunctionalDescription) return NetFunctionalDescription;
	hasRequiredNetFunctionalDescription = 1;
	Object.defineProperty(NetFunctionalDescription, "__esModule", { value: true });
	NetFunctionalDescription.NetFunctionalDescriptionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	NetFunctionalDescription.NetFunctionalDescriptionSchema = zod_1.default.object({
	    functionalShortDescription: zod_1.default.string(),
	    logicalBus: zod_1.default.object({
	        logicalBusId: zod_1.default.string().optional(),
	        busClassDisplayName: zod_1.default.string(),
	        busClass: zod_1.default.string().optional(),
	    }),
	    signalAttributes: zod_1.default
	        .object({
	        power: zod_1.default.boolean().optional(),
	        ground: zod_1.default.boolean().optional(),
	        digital: zod_1.default.boolean().optional(),
	        analog: zod_1.default.boolean().optional(),
	        activeLow: zod_1.default.boolean().optional(),
	    })
	        .optional(),
	    power: zod_1.default
	        .object({
	        type: zod_1.default.enum(['power', 'ground']),
	        nominalVoltage: zod_1.default.number(),
	    })
	        .optional(),
	});
	
	return NetFunctionalDescription;
}

var hasRequiredFunctionalModels;

function requireFunctionalModels () {
	if (hasRequiredFunctionalModels) return FunctionalModels;
	hasRequiredFunctionalModels = 1;
	(function (exports) {
		var __createBinding = (FunctionalModels && FunctionalModels.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (FunctionalModels && FunctionalModels.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireComponentFunctionBusRole(), exports);
		__exportStar(requireComponentFunctionBusType(), exports);
		__exportStar(requireComponentFunctionType(), exports);
		__exportStar(requireFunctionalAddressing(), exports);
		__exportStar(requireFunctionReferenceCondition(), exports);
		__exportStar(requireIComponentFunction(), exports);
		__exportStar(requireIComponentFunctionAbstract(), exports);
		__exportStar(requireIComponentFunctionAssignment(), exports);
		__exportStar(requireIComponentFunctionBoundary(), exports);
		__exportStar(requireIComponentFunctionBoundaryDefinition(), exports);
		__exportStar(requireIComponentFunctionClassParameters(), exports);
		__exportStar(requireIComponentFunctionSignal(), exports);
		__exportStar(requireIComponentFunctionSignalAssignment(), exports);
		__exportStar(requireIComponentFunctionSignalAttributes(), exports);
		__exportStar(requireIComponentFunctionSignalBase(), exports);
		__exportStar(requireIComponentResource(), exports);
		__exportStar(requireIExternalComponent(), exports);
		__exportStar(requireIExternalComponentAssignment(), exports);
		__exportStar(requireIExternalComponentCondition(), exports);
		__exportStar(requireIExternalComponetConnection(), exports);
		__exportStar(requireIFunctionCondition(), exports);
		__exportStar(requireISerializedTraversal(), exports);
		__exportStar(requireNetFunctionalDescription(), exports);
		__exportStar(requireTraversalHelpers(), exports);
		__exportStar(requireTraversalSolvableCharacteristic(), exports);
		
	} (FunctionalModels));
	return FunctionalModels;
}

var InferenceReports$1 = {};

var ComponentBoundaryInferenceReportEntry = {};

var InferenceReportEntryBase = {};

var hasRequiredInferenceReportEntryBase;

function requireInferenceReportEntryBase () {
	if (hasRequiredInferenceReportEntryBase) return InferenceReportEntryBase;
	hasRequiredInferenceReportEntryBase = 1;
	Object.defineProperty(InferenceReportEntryBase, "__esModule", { value: true });
	InferenceReportEntryBase.InferenceReportEntryBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	InferenceReportEntryBase.InferenceReportEntryBaseSchema = zod_1.z.object({
	    source: zod_1.z.string(),
	    inferenceProvider: zod_1.z.string().optional(),
	    inferenceSources: zod_1.z.string().array().optional(),
	    explanation: zod_1.z.string().optional(),
	});
	
	return InferenceReportEntryBase;
}

var hasRequiredComponentBoundaryInferenceReportEntry;

function requireComponentBoundaryInferenceReportEntry () {
	if (hasRequiredComponentBoundaryInferenceReportEntry) return ComponentBoundaryInferenceReportEntry;
	hasRequiredComponentBoundaryInferenceReportEntry = 1;
	Object.defineProperty(ComponentBoundaryInferenceReportEntry, "__esModule", { value: true });
	ComponentBoundaryInferenceReportEntry.ComponentBoundaryInferenceReportEntrySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ComponentBoundary_1 = requireComponentBoundary();
	const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
	ComponentBoundaryInferenceReportEntry.ComponentBoundaryInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('model')
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil')),
	    boundary: ComponentBoundary_1.ComponentBoundarySchema,
	});
	
	return ComponentBoundaryInferenceReportEntry;
}

var ComponentModeInferenceReportEntry = {};

var ModeBase = {};

var IModePrecondition = {};

var hasRequiredIModePrecondition;

function requireIModePrecondition () {
	if (hasRequiredIModePrecondition) return IModePrecondition;
	hasRequiredIModePrecondition = 1;
	Object.defineProperty(IModePrecondition, "__esModule", { value: true });
	IModePrecondition.IModePreconditionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IModeCondition_1 = requireIModeCondition();
	IModePrecondition.IModePreconditionSchema = zod_1.z.object({
	    id: zod_1.z.number(),
	    condition: zod_1.z.lazy(() => IModeCondition_1.IModeConditionSchema),
	});
	
	return IModePrecondition;
}

var IModeStateConfig = {};

var hasRequiredIModeStateConfig;

function requireIModeStateConfig () {
	if (hasRequiredIModeStateConfig) return IModeStateConfig;
	hasRequiredIModeStateConfig = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IModeStateConfigSchema = exports.IModeStateDefinitionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryLevel_1 = requireBoundaryLevel();
		const ModeStateType_1 = requireModeStateType();
		exports.IModeStateDefinitionSchema = zod_1.z.object({
		    stateId: zod_1.z.nativeEnum(ModeStateType_1.ModeStateType),
		    isNominalState: zod_1.z.boolean().optional(),
		    unreachableSeverity: BoundaryLevel_1.BoundaryLevelSchema.optional(),
		});
		exports.IModeStateConfigSchema = zod_1.z.object({
		    states: exports.IModeStateDefinitionSchema.array().refine((states) => states.filter((state) => state.isNominalState === true).length <= 1, { message: 'At most one mode state may set isNominalState' }),
		});
		
	} (IModeStateConfig));
	return IModeStateConfig;
}

var hasRequiredModeBase;

function requireModeBase () {
	if (hasRequiredModeBase) return ModeBase;
	hasRequiredModeBase = 1;
	Object.defineProperty(ModeBase, "__esModule", { value: true });
	ModeBase.ModeBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IModePrecondition_1 = requireIModePrecondition();
	const IModeStateConfig_1 = requireIModeStateConfig();
	ModeBase.ModeBaseSchema = zod_1.z.object({
	    id: zod_1.z.string(),
	    preconditions: zod_1.z
	        .lazy(() => IModePrecondition_1.IModePreconditionSchema)
	        .array()
	        .optional(),
	    stateConfig: IModeStateConfig_1.IModeStateConfigSchema.optional(),
	});
	
	return ModeBase;
}

var hasRequiredComponentModeInferenceReportEntry;

function requireComponentModeInferenceReportEntry () {
	if (hasRequiredComponentModeInferenceReportEntry) return ComponentModeInferenceReportEntry;
	hasRequiredComponentModeInferenceReportEntry = 1;
	Object.defineProperty(ComponentModeInferenceReportEntry, "__esModule", { value: true });
	ComponentModeInferenceReportEntry.ComponentModeInferenceReportEntrySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ModeBase_1 = requireModeBase();
	const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
	ComponentModeInferenceReportEntry.ComponentModeInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('model')
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil')),
	    mode: ModeBase_1.ModeBaseSchema,
	});
	
	return ComponentModeInferenceReportEntry;
}

var ComponentParameterInferenceReportEntry = {};

var VarianceTypes = {};

var SIUnitDeclaration = {};

var hasRequiredSIUnitDeclaration;

function requireSIUnitDeclaration () {
	if (hasRequiredSIUnitDeclaration) return SIUnitDeclaration;
	hasRequiredSIUnitDeclaration = 1;
	Object.defineProperty(SIUnitDeclaration, "__esModule", { value: true });
	SIUnitDeclaration.SIUnitDeclarationSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	SIUnitDeclaration.SIUnitDeclarationSchema = zod_1.z.object({
	    short: zod_1.z.string().optional(),
	    long: zod_1.z.string().optional(),
	    alternateNames: zod_1.z.string().array().optional(),
	    usesSIPrefixes: zod_1.z.boolean().optional(),
	    epsilon: zod_1.z.number().optional(),
	});
	
	return SIUnitDeclaration;
}

var hasRequiredVarianceTypes;

function requireVarianceTypes () {
	if (hasRequiredVarianceTypes) return VarianceTypes;
	hasRequiredVarianceTypes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ParsedParameterSchema = exports.VariancePropertiesSchema = exports.StoredValueVarianceSchema = exports.ValueVarianceSchema = void 0;
		exports.isValueVariance = isValueVariance;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SIUnitDeclaration_1 = requireSIUnitDeclaration();
		function isValueVariance(v) {
		    if (typeof v !== 'object' || v === null || !('value' in v)) {
		        return false;
		    }
		    const keys = Object.keys(v);
		    return keys.every((k) => k === 'value' || k === 'variance');
		}
		exports.ValueVarianceSchema = zod_1.z.object({
		    value: zod_1.z.number().optional(),
		    variance: zod_1.z.string().optional(),
		});
		exports.StoredValueVarianceSchema = zod_1.z.object({
		    value: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		    variance: zod_1.z.string().optional(),
		});
		exports.VariancePropertiesSchema = zod_1.z.object({
		    value: zod_1.z.number(),
		    units: SIUnitDeclaration_1.SIUnitDeclarationSchema,
		    singleSided: zod_1.z.boolean(),
		});
		exports.ParsedParameterSchema = zod_1.z.record(zod_1.z.string(), exports.ValueVarianceSchema);
		
	} (VarianceTypes));
	return VarianceTypes;
}

var hasRequiredComponentParameterInferenceReportEntry;

function requireComponentParameterInferenceReportEntry () {
	if (hasRequiredComponentParameterInferenceReportEntry) return ComponentParameterInferenceReportEntry;
	hasRequiredComponentParameterInferenceReportEntry = 1;
	Object.defineProperty(ComponentParameterInferenceReportEntry, "__esModule", { value: true });
	ComponentParameterInferenceReportEntry.ComponentParameterInferenceReportEntrySchemaTyped = ComponentParameterInferenceReportEntry.ComponentParameterInferenceReportEntrySchema = void 0;
	ComponentParameterInferenceReportEntry.pickBestComponentParameterInferenceReportEntry = pickBestComponentParameterInferenceReportEntry;
	const zod_1 = /*@__PURE__*/ requireZod();
	const VarianceTypes_1 = requireVarianceTypes();
	const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
	function pickBestComponentParameterInferenceReportEntry(a, b) {
	    if (!a) {
	        return b;
	    }
	    if (!b) {
	        return a;
	    }
	    const aRank = getSourceRank(a.source);
	    const bRank = getSourceRank(b.source);
	    if (aRank > bRank) {
	        return a;
	    }
	    if (bRank > aRank) {
	        return b;
	    }
	    return a;
	}
	function getSourceRank(source) {
	    switch (source) {
	        case 'default':
	            return 0;
	        case 'specified':
	        case 'module-value':
	            return 1;
	        case 'inferred':
	            return 2;
	        case 'parser':
	            return 3;
	        case 'partner':
	            return 4;
	        case 'hil':
	            return 6;
	        default:
	            return 5;
	    }
	}
	ComponentParameterInferenceReportEntry.ComponentParameterInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('specified')
	        .or(zod_1.z.literal('partner'))
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil'))
	        .or(zod_1.z.literal('module-value'))
	        .or(zod_1.z.literal('parser')),
	    value: zod_1.z.union([zod_1.z.union([zod_1.z.string(), zod_1.z.number()]), VarianceTypes_1.ValueVarianceSchema]),
	    disabled: zod_1.z.boolean().optional(),
	    sourceField: zod_1.z.string().nullish(),
	    varianceSourceField: zod_1.z.string().nullish(),
	});
	const ComponentParameterInferenceReportEntrySchemaTyped = (typeT) => InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('specified')
	        .or(zod_1.z.literal('partner'))
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil'))
	        .or(zod_1.z.literal('module-value'))
	        .or(zod_1.z.literal('parser')),
	    value: zod_1.z.union([typeT, VarianceTypes_1.ValueVarianceSchema]),
	    disabled: zod_1.z.boolean().optional(),
	    sourceField: zod_1.z.string().nullish(),
	    varianceSourceField: zod_1.z.string().nullish(),
	});
	ComponentParameterInferenceReportEntry.ComponentParameterInferenceReportEntrySchemaTyped = ComponentParameterInferenceReportEntrySchemaTyped;
	
	return ComponentParameterInferenceReportEntry;
}

var ComponentSignalInferenceReportEntry = {};

var hasRequiredComponentSignalInferenceReportEntry;

function requireComponentSignalInferenceReportEntry () {
	if (hasRequiredComponentSignalInferenceReportEntry) return ComponentSignalInferenceReportEntry;
	hasRequiredComponentSignalInferenceReportEntry = 1;
	Object.defineProperty(ComponentSignalInferenceReportEntry, "__esModule", { value: true });
	ComponentSignalInferenceReportEntry.ComponentSignalInferenceReportEntrySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const Signal_1 = requireSignal();
	const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
	ComponentSignalInferenceReportEntry.ComponentSignalInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('specified')
	        .or(zod_1.z.literal('partner'))
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil')),
	    priority: zod_1.z.number().optional(),
	    signalId: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
	    properties: zod_1.z.lazy(() => Signal_1.SignalSchema.omit({ id: true, name: true, pinRefs: true, padRefs: true })),
	});
	
	return ComponentSignalInferenceReportEntry;
}

var ComponentFunctionInferenceReportEntry = {};

var hasRequiredComponentFunctionInferenceReportEntry;

function requireComponentFunctionInferenceReportEntry () {
	if (hasRequiredComponentFunctionInferenceReportEntry) return ComponentFunctionInferenceReportEntry;
	hasRequiredComponentFunctionInferenceReportEntry = 1;
	Object.defineProperty(ComponentFunctionInferenceReportEntry, "__esModule", { value: true });
	ComponentFunctionInferenceReportEntry.ComponentFunctionInferenceReportEntrySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
	const IComponentFunction_1 = requireIComponentFunction();
	ComponentFunctionInferenceReportEntry.ComponentFunctionInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('model')
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil')),
	    priority: zod_1.z.number().optional(),
	    function: IComponentFunction_1.IComponentFunctionSchema,
	});
	
	return ComponentFunctionInferenceReportEntry;
}

var ComponentResourceInferenceReportEntry = {};

var hasRequiredComponentResourceInferenceReportEntry;

function requireComponentResourceInferenceReportEntry () {
	if (hasRequiredComponentResourceInferenceReportEntry) return ComponentResourceInferenceReportEntry;
	hasRequiredComponentResourceInferenceReportEntry = 1;
	Object.defineProperty(ComponentResourceInferenceReportEntry, "__esModule", { value: true });
	ComponentResourceInferenceReportEntry.ComponentResourceInferenceReportEntrySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
	const IComponentResource_1 = requireIComponentResource();
	ComponentResourceInferenceReportEntry.ComponentResourceInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
	    source: zod_1.z
	        .literal('model')
	        .or(zod_1.z.literal('inferred'))
	        .or(zod_1.z.literal('default'))
	        .or(zod_1.z.literal('hil')),
	    priority: zod_1.z.number().optional(),
	    resource: IComponentResource_1.IComponentResourceSchema,
	});
	
	return ComponentResourceInferenceReportEntry;
}

var InferenceReports = {};

var hasRequiredInferenceReports$1;

function requireInferenceReports$1 () {
	if (hasRequiredInferenceReports$1) return InferenceReports;
	hasRequiredInferenceReports$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IInferenceReportWithPostInferenceActionsSchema = exports.ISubModelInferenceReportEntrySchema = exports.InferenceReportSchema = exports.ComponentParameterInferenceReportSchema = void 0;
		exports.applyInferenceReportEntriesToSignals = applyInferenceReportEntriesToSignals;
		exports.applyInferenceReportEntriesToSignal = applyInferenceReportEntriesToSignal;
		const SPICE_1 = requireSPICE();
		const zod_1 = /*@__PURE__*/ requireZod();
		const ComponentBoundaryInferenceReportEntry_1 = requireComponentBoundaryInferenceReportEntry();
		const ComponentSignalInferenceReportEntry_1 = requireComponentSignalInferenceReportEntry();
		const ComponentModeInferenceReportEntry_1 = requireComponentModeInferenceReportEntry();
		const ComponentFunctionInferenceReportEntry_1 = requireComponentFunctionInferenceReportEntry();
		const ComponentResourceInferenceReportEntry_1 = requireComponentResourceInferenceReportEntry();
		const InferenceReportEntryBase_1 = requireInferenceReportEntryBase();
		exports.ComponentParameterInferenceReportSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.object({
		    source: zod_1.z.string(),
		    value: zod_1.z.any().optional(),
		    disabled: zod_1.z.boolean().optional(),
		}));
		exports.InferenceReportSchema = zod_1.z.object({
		    modes: zod_1.z.lazy(() => ComponentModeInferenceReportEntry_1.ComponentModeInferenceReportEntrySchema.array().optional()),
		    boundaries: zod_1.z.lazy(() => ComponentBoundaryInferenceReportEntry_1.ComponentBoundaryInferenceReportEntrySchema.array().optional()),
		    parameters: zod_1.z.lazy(() => exports.ComponentParameterInferenceReportSchema.optional()),
		    signals: zod_1.z.lazy(() => ComponentSignalInferenceReportEntry_1.ComponentSignalInferenceReportEntrySchema.array().optional()),
		    submodels: zod_1.z.lazy(() => exports.ISubModelInferenceReportEntrySchema.array().optional()),
		    functions: zod_1.z.lazy(() => ComponentFunctionInferenceReportEntry_1.ComponentFunctionInferenceReportEntrySchema.array().optional()),
		    resources: zod_1.z.lazy(() => ComponentResourceInferenceReportEntry_1.ComponentResourceInferenceReportEntrySchema.array().optional()),
		});
		exports.ISubModelInferenceReportEntrySchema = InferenceReportEntryBase_1.InferenceReportEntryBaseSchema.extend({
		    source: zod_1.z
		        .literal('model')
		        .or(zod_1.z.literal('inferred'))
		        .or(zod_1.z.literal('default')),
		    submodel: SPICE_1.ISubModelOptsSchema,
		});
		exports.IInferenceReportWithPostInferenceActionsSchema = exports.InferenceReportSchema.extend({
		    postInferenceActions: zod_1.z
		        .custom((value) => typeof value === 'function')
		        .array()
		        .optional(),
		});
		function applyInferenceReportEntriesToSignals(reportEntries, signals) {
		    const resultSignals = signals.map((signal) => applyInferenceReportEntriesToSignal(reportEntries?.filter((e) => e.signalId === signal.id), signal));
		    return resultSignals;
		}
		function applyInferenceReportEntriesToSignal(reportEntries, resultSignal) {
		    reportEntries.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
		    for (const entry of reportEntries) {
		        if (entry.signalId !== resultSignal.id) {
		            continue;
		        }
		        let metadata;
		        if (resultSignal.metadata) {
		            if (entry.properties.metadata) {
		                metadata = {
		                    ...resultSignal.metadata,
		                    ...entry.properties.metadata,
		                };
		            }
		            else {
		                metadata = resultSignal.metadata;
		            }
		        }
		        else if (entry.properties.metadata) {
		            metadata = entry.properties.metadata;
		        }
		        let parametrics;
		        if (resultSignal.parametrics) {
		            if (entry.properties.parametrics) {
		                parametrics = {
		                    ...resultSignal.parametrics,
		                    ...entry.properties.parametrics,
		                };
		            }
		            else {
		                parametrics = resultSignal.parametrics;
		            }
		        }
		        else if (entry.properties.parametrics) {
		            parametrics = entry.properties.parametrics;
		        }
		        resultSignal = {
		            ...resultSignal,
		            ...Object.fromEntries(Object.entries(entry.properties).filter(([, v]) => v !== undefined)),
		            parametrics,
		            metadata,
		        };
		    }
		    return resultSignal;
		}
		
	} (InferenceReports));
	return InferenceReports;
}

var hasRequiredInferenceReports;

function requireInferenceReports () {
	if (hasRequiredInferenceReports) return InferenceReports$1;
	hasRequiredInferenceReports = 1;
	(function (exports) {
		var __createBinding = (InferenceReports$1 && InferenceReports$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (InferenceReports$1 && InferenceReports$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireComponentBoundaryInferenceReportEntry(), exports);
		__exportStar(requireComponentModeInferenceReportEntry(), exports);
		__exportStar(requireComponentParameterInferenceReportEntry(), exports);
		__exportStar(requireComponentSignalInferenceReportEntry(), exports);
		__exportStar(requireComponentFunctionInferenceReportEntry(), exports);
		__exportStar(requireComponentResourceInferenceReportEntry(), exports);
		__exportStar(requireInferenceReportEntryBase(), exports);
		__exportStar(requireInferenceReports$1(), exports);
		
	} (InferenceReports$1));
	return InferenceReports$1;
}

var MergeInferenceReports = {};

var hasRequiredMergeInferenceReports;

function requireMergeInferenceReports () {
	if (hasRequiredMergeInferenceReports) return MergeInferenceReports;
	hasRequiredMergeInferenceReports = 1;
	Object.defineProperty(MergeInferenceReports, "__esModule", { value: true });
	MergeInferenceReports.mergeInferenceReports = mergeInferenceReports;
	const toArray_1 = requireToArray();
	const ComponentParameterInferenceReportEntry_1 = requireComponentParameterInferenceReportEntry();
	const ValueValidation_1 = requireValueValidation();
	function mergeInferenceReports(original, additional, opts = {}) {
	    if (typeof original === 'undefined') {
	        return additional ?? {};
	    }
	    if (typeof additional === 'undefined') {
	        return original;
	    }
	    const boundaries = new Map();
	    for (const boundary of [...(0, toArray_1.toArray)(original.boundaries), ...(0, toArray_1.toArray)(additional.boundaries)]) {
	        boundaries.set(boundary.boundary.id, boundary);
	    }
	    const modes = new Map();
	    for (const mode of [...(0, toArray_1.toArray)(original.modes), ...(0, toArray_1.toArray)(additional.modes)]) {
	        modes.set(mode.mode.id, mode);
	    }
	    const functions = new Map();
	    for (const func of [...(0, toArray_1.toArray)(original.functions), ...(0, toArray_1.toArray)(additional.functions)]) {
	        const existing = functions.get(func.function.name);
	        if (!existing || (func.priority ?? 0) >= (existing.priority ?? 0)) {
	            functions.set(func.function.name, func);
	        }
	    }
	    const resources = new Map();
	    for (const resource of [...(0, toArray_1.toArray)(original.resources), ...(0, toArray_1.toArray)(additional.resources)]) {
	        const existing = resources.get(resource.resource.name);
	        if (!existing || (resource.priority ?? 0) >= (existing.priority ?? 0)) {
	            resources.set(resource.resource.name, resource);
	        }
	    }
	    const subModels = new Map();
	    for (const submodel of [...(0, toArray_1.toArray)(original.submodels), ...(0, toArray_1.toArray)(additional.submodels)]) {
	        const modelId = submodel.submodel.subModelId;
	        if (subModels.has(modelId)) {
	            subModels.set(modelId, {
	                source: subModels.get(modelId)?.source ?? submodel.source,
	                submodel: submodel.submodel,
	                ...mergeInferenceReports(subModels.get(modelId), submodel),
	            });
	        }
	        else {
	            subModels.set(modelId, submodel);
	        }
	    }
	    const result = {
	        parameters: {},
	    };
	    if (boundaries.size > 0) {
	        result.boundaries = Array.from(boundaries.values());
	    }
	    if (modes.size > 0) {
	        result.modes = Array.from(modes.values());
	    }
	    if (functions.size > 0) {
	        result.functions = Array.from(functions.values());
	    }
	    if (resources.size > 0) {
	        result.resources = Array.from(resources.values());
	    }
	    if (subModels.size > 0) {
	        result.submodels = Array.from(subModels.values());
	    }
	    if (additional.parameters) {
	        result.parameters = Object.fromEntries(Object.entries(original.parameters ?? {}).filter(([, v]) => !v.disabled));
	        for (const [key, value] of Object.entries(additional.parameters)) {
	            if (value?.disabled) {
	                continue;
	            }
	            if ((0, ValueValidation_1.isEmptyValue)(value)) {
	                continue;
	            }
	            result.parameters[key] = (0, ComponentParameterInferenceReportEntry_1.pickBestComponentParameterInferenceReportEntry)(value, result.parameters[key]);
	        }
	    }
	    else {
	        result.parameters = original.parameters;
	    }
	    if (result.parameters && !Object.keys(result.parameters).length) {
	        delete result.parameters;
	    }
	    if (original.signals) {
	        if (additional.signals) {
	            result.signals = [...original.signals, ...additional.signals];
	        }
	        else {
	            result.signals = original.signals;
	        }
	    }
	    else {
	        result.signals = additional.signals;
	    }
	    if (opts.dedupeSignalEntries && result.signals) {
	        result.signals = result.signals.reduce((acc, signal) => {
	            for (const existing of acc) {
	                if (signal.signalId === existing.signalId) {
	                    const deduped = opts.dedupeSignalEntries(existing, signal);
	                    if (deduped) {
	                        return [...acc.filter((s) => s !== existing), deduped];
	                    }
	                }
	            }
	            acc.push(signal);
	            return acc;
	        }, []);
	    }
	    result.postInferenceActions = [...(original.postInferenceActions ?? []), ...(additional.postInferenceActions ?? [])];
	    if (!result.postInferenceActions.length) {
	        delete result.postInferenceActions;
	    }
	    return result;
	}
	
	return MergeInferenceReports;
}

var MessageResult = {};

var hasRequiredMessageResult;

function requireMessageResult () {
	if (hasRequiredMessageResult) return MessageResult;
	hasRequiredMessageResult = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TGenericErrorMessageResult = exports.AggregateMessageResultSchemaDefault = exports.MessageResultSchemaDefault = void 0;
		exports.MessageResultSchema = MessageResultSchema;
		exports.AggregateMessageResultSchema = AggregateMessageResultSchema;
		exports.PossibleAggregateMessageResultSchema = PossibleAggregateMessageResultSchema;
		exports.computeAggragateMessageResultSeverity = computeAggragateMessageResultSeverity;
		exports.errorToMessageResult = errorToMessageResult;
		exports.scoreMessageResult = scoreMessageResult;
		exports.stripMessageResultExtraProperties = stripMessageResultExtraProperties;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryLevel_1 = requireBoundaryLevel();
		const BoundaryLevel_2 = requireBoundaryLevel();
		function MessageResultSchema(messageType, innerResultSchema) {
		    return zod_1.default
		        .object({
		        type: messageType,
		        message: zod_1.default.string(),
		        severity: zod_1.default.nativeEnum(BoundaryLevel_2.BoundaryLevel).optional(),
		        innerResult: innerResultSchema.optional(),
		    })
		        .passthrough();
		}
		exports.MessageResultSchemaDefault = MessageResultSchema(zod_1.default.string(), zod_1.default.lazy(() => exports.MessageResultSchemaDefault));
		function AggregateMessageResultSchema(typeEnum, innerResultSchema) {
		    return zod_1.default
		        .object({
		        type: typeEnum,
		        message: zod_1.default.string(),
		        severity: zod_1.default.nativeEnum(BoundaryLevel_2.BoundaryLevel).optional(),
		        innerResults: innerResultSchema.array(),
		    })
		        .passthrough();
		}
		exports.AggregateMessageResultSchemaDefault = AggregateMessageResultSchema(zod_1.default.string(), exports.MessageResultSchemaDefault);
		function PossibleAggregateMessageResultSchema(typeEnum, innerResultSchema) {
		    return zod_1.default
		        .object({
		        type: zod_1.default.nativeEnum(typeEnum),
		        message: zod_1.default.string(),
		        severity: zod_1.default.nativeEnum(BoundaryLevel_2.BoundaryLevel).optional(),
		        innerResults: innerResultSchema.array().optional(),
		    })
		        .passthrough();
		}
		function computeAggragateMessageResultSeverity(result) {
		    result.severity =
		        result.innerResults?.reduce((p, c) => (0, BoundaryLevel_1.getHighestBoundaryLevel)(p, c.severity ?? BoundaryLevel_2.BoundaryLevel.Recommendation), BoundaryLevel_2.BoundaryLevel.Recommendation) ?? BoundaryLevel_2.BoundaryLevel.Recommendation;
		}
		var TGenericErrorMessageResult;
		(function (TGenericErrorMessageResult) {
		    TGenericErrorMessageResult["UnknownError"] = "UnknownError";
		    TGenericErrorMessageResult["Error"] = "Error";
		    TGenericErrorMessageResult["AggregateError"] = "AggregateError";
		})(TGenericErrorMessageResult || (exports.TGenericErrorMessageResult = TGenericErrorMessageResult = {}));
		function errorToMessageResult(error) {
		    if (error && typeof error === 'object') {
		        if (typeof AggregateError !== 'undefined' && error instanceof AggregateError) {
		            return {
		                type: TGenericErrorMessageResult.AggregateError,
		                message: 'An aggregate error occurred',
		                severity: BoundaryLevel_2.BoundaryLevel.Error,
		                innerResults: error.errors.map((innerError) => errorToMessageResult(innerError instanceof Error ? innerError : new Error(String(innerError)))),
		            };
		        }
		        const walkable = {};
		        const cause = error.innerError;
		        if (cause instanceof Error) {
		            walkable.innerResult = errorToMessageResult(cause);
		        }
		        let jsonProps = {};
		        if (error instanceof Error && 'toJSON' in error && typeof error.toJSON === 'function') {
		            jsonProps = error.toJSON();
		            delete jsonProps.innerError;
		        }
		        if (error instanceof Error) {
		            return {
		                ...jsonProps,
		                ...walkable,
		                type: TGenericErrorMessageResult.Error,
		                message: error.message,
		                severity: BoundaryLevel_2.BoundaryLevel.Error,
		                stack: error.stack || '',
		                errorClass: error?.constructor?.name,
		            };
		        }
		        const err = error;
		        if ('message' in err && typeof err.message === 'string') {
		            return {
		                ...jsonProps,
		                ...walkable,
		                type: TGenericErrorMessageResult.Error,
		                message: err.message,
		                severity: BoundaryLevel_2.BoundaryLevel.Error,
		                stack: err.stack?.toString() || new Error().stack || '',
		            };
		        }
		    }
		    return {
		        type: TGenericErrorMessageResult.UnknownError,
		        message: 'An unknown error occurred',
		        severity: BoundaryLevel_2.BoundaryLevel.Error,
		        stack: new Error().stack || '',
		    };
		}
		function scoreMessageResult(result, errorScore = 0, warningScore = 0.5, recommendationScore = 0.9, defaultScore = 1) {
		    if (result.innerResults?.length) {
		        result.score =
		            result.innerResults
		                .map((innerResult) => scoreMessageResult(innerResult, errorScore, warningScore, recommendationScore, defaultScore))
		                .reduce((a, b) => a + b, 0) / result.innerResults.length;
		    }
		    else if (result.severity === BoundaryLevel_2.BoundaryLevel.Error) {
		        result.score = errorScore;
		    }
		    else if (result.severity === BoundaryLevel_2.BoundaryLevel.Warning) {
		        result.score = warningScore;
		    }
		    else if (result.severity === BoundaryLevel_2.BoundaryLevel.Recommendation) {
		        result.score = recommendationScore;
		    }
		    else {
		        result.score = defaultScore;
		    }
		    return result.score;
		}
		function stripMessageResultExtraProperties(result) {
		    const cleanResult = {
		        type: result.type,
		        message: result.message,
		        severity: result.severity,
		    };
		    if (result.innerResults) {
		        cleanResult.innerResults = result.innerResults.map((innerResult) => stripMessageResultExtraProperties(innerResult));
		    }
		    return cleanResult;
		}
		
	} (MessageResult));
	return MessageResult;
}

var Migration = {};

var hasRequiredMigration;

function requireMigration () {
	if (hasRequiredMigration) return Migration;
	hasRequiredMigration = 1;
	Object.defineProperty(Migration, "__esModule", { value: true });
	Migration.MigrationResultType = void 0;
	Migration.entityMigrationFactory = entityMigrationFactory;
	Migration.computeMigrationResultSeverity = computeMigrationResultSeverity;
	const MessageResult_1 = requireMessageResult();
	var MigrationResultType;
	(function (MigrationResultType) {
	    MigrationResultType["PropertyAdded"] = "prop-added";
	    MigrationResultType["PropertyModified"] = "prop-modified";
	    MigrationResultType["PropertyDeleted"] = "prop-deleted";
	    MigrationResultType["ProjectMigration"] = "project-migration";
	    MigrationResultType["AltiumProjectTransformation"] = "altium-project-transformation";
	    MigrationResultType["ComponentMigrationCollision"] = "component-migration-collision";
	    MigrationResultType["ComponentMigration"] = "component-migration";
	    MigrationResultType["SignalMigration"] = "signal-migration";
	    MigrationResultType["BoundaryMigration"] = "boundary-migration";
	    MigrationResultType["ModeMigration"] = "mode-migration";
	    MigrationResultType["SigmaModelMigration"] = "model-migration";
	    MigrationResultType["GenericMigration"] = "generic-migration";
	})(MigrationResultType || (Migration.MigrationResultType = MigrationResultType = {}));
	function entityMigrationFactory(migrations, migrationType) {
	    return (entity) => {
	        const migratedEntity = { ...entity };
	        const migrationResult = {
	            type: migrationType,
	            isModified: false,
	            entity: migratedEntity,
	            message: 'Migrated entity',
	            innerResults: [],
	        };
	        for (const [prop, migrator] of Object.entries(migrations)) {
	            const selectedField = entity[prop];
	            if (selectedField !== undefined) {
	                const fieldMigrationResult = migrator(selectedField);
	                if (fieldMigrationResult.isModified) {
	                    migrationResult.isModified = true;
	                    migrationResult.innerResults.push(fieldMigrationResult);
	                    migrationResult.entity[prop] = fieldMigrationResult.entity;
	                }
	            }
	        }
	        return migrationResult;
	    };
	}
	function computeMigrationResultSeverity(result) {
	    (0, MessageResult_1.computeAggragateMessageResultSeverity)(result);
	}
	
	return Migration;
}

var ModuleResolution = {};

var IComponentModelResolutionResult = {};

var hasRequiredIComponentModelResolutionResult;

function requireIComponentModelResolutionResult () {
	if (hasRequiredIComponentModelResolutionResult) return IComponentModelResolutionResult;
	hasRequiredIComponentModelResolutionResult = 1;
	Object.defineProperty(IComponentModelResolutionResult, "__esModule", { value: true });
	
	return IComponentModelResolutionResult;
}

var IComponentInstanceSummary = {};

var hasRequiredIComponentInstanceSummary;

function requireIComponentInstanceSummary () {
	if (hasRequiredIComponentInstanceSummary) return IComponentInstanceSummary;
	hasRequiredIComponentInstanceSummary = 1;
	Object.defineProperty(IComponentInstanceSummary, "__esModule", { value: true });
	
	return IComponentInstanceSummary;
}

var hasRequiredModuleResolution;

function requireModuleResolution () {
	if (hasRequiredModuleResolution) return ModuleResolution;
	hasRequiredModuleResolution = 1;
	(function (exports) {
		var __createBinding = (ModuleResolution && ModuleResolution.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ModuleResolution && ModuleResolution.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireIComponentModelResolutionResult(), exports);
		__exportStar(requireIComponentInstanceSummary(), exports);
		
	} (ModuleResolution));
	return ModuleResolution;
}

var BoundaryAssertionState = {};

var hasRequiredBoundaryAssertionState;

function requireBoundaryAssertionState () {
	if (hasRequiredBoundaryAssertionState) return BoundaryAssertionState;
	hasRequiredBoundaryAssertionState = 1;
	Object.defineProperty(BoundaryAssertionState, "__esModule", { value: true });
	BoundaryAssertionState.BoundaryAssertionStateSchema = BoundaryAssertionState.BoundaryAssertionState = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var BoundaryAssertionState$1;
	(function (BoundaryAssertionState) {
	    BoundaryAssertionState[BoundaryAssertionState["NotAsserting"] = 0] = "NotAsserting";
	    BoundaryAssertionState[BoundaryAssertionState["Unresolved"] = 1] = "Unresolved";
	    BoundaryAssertionState[BoundaryAssertionState["Implication"] = 2] = "Implication";
	    BoundaryAssertionState[BoundaryAssertionState["Assertion"] = 3] = "Assertion";
	})(BoundaryAssertionState$1 || (BoundaryAssertionState.BoundaryAssertionState = BoundaryAssertionState$1 = {}));
	BoundaryAssertionState.BoundaryAssertionStateSchema = zod_1.z.nativeEnum(BoundaryAssertionState$1);
	
	return BoundaryAssertionState;
}

var BoundaryDefinitionTypes = {};

var hasRequiredBoundaryDefinitionTypes;

function requireBoundaryDefinitionTypes () {
	if (hasRequiredBoundaryDefinitionTypes) return BoundaryDefinitionTypes;
	hasRequiredBoundaryDefinitionTypes = 1;
	Object.defineProperty(BoundaryDefinitionTypes, "__esModule", { value: true });
	BoundaryDefinitionTypes.IBoundaryDefinitionTypesSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const IConnectionBoundaryDefinition_1 = requireIConnectionBoundaryDefinition();
	const INumericalBoundaryDefinition_1 = requireINumericalBoundaryDefinition();
	BoundaryDefinitionTypes.IBoundaryDefinitionTypesSchema = zod_1.z.lazy(() => zod_1.z.discriminatedUnion('type', [INumericalBoundaryDefinition_1.INumericalBoundaryDefinitionSchema, IConnectionBoundaryDefinition_1.IConnectionBoundaryDefinitionSchema]));
	
	return BoundaryDefinitionTypes;
}

var CadSemanticFileType = {};

var hasRequiredCadSemanticFileType;

function requireCadSemanticFileType () {
	if (hasRequiredCadSemanticFileType) return CadSemanticFileType;
	hasRequiredCadSemanticFileType = 1;
	Object.defineProperty(CadSemanticFileType, "__esModule", { value: true });
	CadSemanticFileType.CadSemanticFileTypeSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	CadSemanticFileType.CadSemanticFileTypeSchema = zod_1.z.enum([
	    'board',
	    'schematic',
	    'lib',
	    'dru',
	    'project',
	    'other',
	    'bom',
	    'stackup',
	    'netlist',
	]);
	
	return CadSemanticFileType;
}

var CadTool = {};

var hasRequiredCadTool;

function requireCadTool () {
	if (hasRequiredCadTool) return CadTool;
	hasRequiredCadTool = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DeclarableCadToolSchema = exports.CadToolSchema = void 0;
		exports.getSupportedCadToolDisplayNames = getSupportedCadToolDisplayNames;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.CadToolSchema = zod_1.z.enum([
		    'kicad',
		    'eagle',
		    'altium',
		    'orcad',
		    'allegro',
		    'xpedition',
		    'cadstrom',
		]);
		exports.DeclarableCadToolSchema = exports.CadToolSchema.exclude(['cadstrom']);
		const CAD_TOOL_DISPLAY_NAMES = {
		    kicad: 'KiCad',
		    eagle: 'Eagle',
		    altium: 'Altium',
		    orcad: 'OrCAD',
		    allegro: 'Allegro X System Capture',
		    xpedition: 'Xpedition',
		    cadstrom: 'Cadstrom',
		};
		function getSupportedCadToolDisplayNames() {
		    return exports.CadToolSchema.options.filter((tool) => tool !== 'cadstrom').map((tool) => CAD_TOOL_DISPLAY_NAMES[tool]);
		}
		
	} (CadTool));
	return CadTool;
}

var component = {};

var SchFootprint = {};

var Footprint = {};

var DrawingWithLayers = {};

var AnyDrawingSchema = {};

var SchematicReference = {};

var Module = {};

var ColorOverride = {};

var hasRequiredColorOverride;

function requireColorOverride () {
	if (hasRequiredColorOverride) return ColorOverride;
	hasRequiredColorOverride = 1;
	Object.defineProperty(ColorOverride, "__esModule", { value: true });
	ColorOverride.ColorOverrideSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	ColorOverride.ColorOverrideSchema = zod_1.z.tuple([
	    zod_1.z.number(),
	    zod_1.z.number(),
	    zod_1.z.number(),
	    zod_1.z.number(),
	]);
	
	return ColorOverride;
}

var ModuleDrawProperty = {};

var Text = {};

var DrawingBase = {};

var hasRequiredDrawingBase;

function requireDrawingBase () {
	if (hasRequiredDrawingBase) return DrawingBase;
	hasRequiredDrawingBase = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DrawingBaseSchema = exports.DrawingNetPointerSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ColorOverride_1 = requireColorOverride();
		exports.DrawingNetPointerSchema = zod_1.z.object({
		    i: zod_1.z.string(),
		    n: zod_1.z.string(),
		});
		exports.DrawingBaseSchema = zod_1.z.object({
		    type: zod_1.z.string(),
		    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean()])).optional(),
		    net: zod_1.z.string().optional(),
		    nets: exports.DrawingNetPointerSchema.array().optional(),
		    colorOverride: ColorOverride_1.ColorOverrideSchema.optional(),
		});
		
	} (DrawingBase));
	return DrawingBase;
}

var TextTransform = {};

var hasRequiredTextTransform;

function requireTextTransform () {
	if (hasRequiredTextTransform) return TextTransform;
	hasRequiredTextTransform = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TextTransformSchema = exports.TextTransformReplaceSchema = exports.TextTransformBarSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.TextTransformBarSchema = zod_1.z.object({
		    type: zod_1.z.literal('bar'),
		    from: zod_1.z.number(),
		    to: zod_1.z.number(),
		    width: zod_1.z.number().optional(),
		});
		exports.TextTransformReplaceSchema = zod_1.z.object({
		    type: zod_1.z.literal('replace'),
		    start: zod_1.z.number(),
		    length: zod_1.z.number(),
		    value: zod_1.z.string(),
		});
		exports.TextTransformSchema = zod_1.z.discriminatedUnion('type', [
		    exports.TextTransformBarSchema,
		    exports.TextTransformReplaceSchema,
		]);
		
	} (TextTransform));
	return TextTransform;
}

var XYPosition = {};

var hasRequiredXYPosition;

function requireXYPosition () {
	if (hasRequiredXYPosition) return XYPosition;
	hasRequiredXYPosition = 1;
	Object.defineProperty(XYPosition, "__esModule", { value: true });
	XYPosition.XYPositionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	XYPosition.XYPositionSchema = zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()]);
	
	return XYPosition;
}

var TextAttr = {};

var hasRequiredTextAttr;

function requireTextAttr () {
	if (hasRequiredTextAttr) return TextAttr;
	hasRequiredTextAttr = 1;
	Object.defineProperty(TextAttr, "__esModule", { value: true });
	TextAttr.TextAttrSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	TextAttr.TextAttrSchema = zod_1.z.enum(['bold', 'italic']);
	
	return TextAttr;
}

var hasRequiredText;

function requireText () {
	if (hasRequiredText) return Text;
	hasRequiredText = 1;
	Object.defineProperty(Text, "__esModule", { value: true });
	Text.TextSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const TextTransform_1 = requireTextTransform();
	const XYPosition_1 = requireXYPosition();
	const TextAttr_1 = requireTextAttr();
	Text.TextSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    ref: zod_1.z.string().optional(),
	    value: zod_1.z.string(),
	    type: zod_1.z.literal('text'),
	    size: zod_1.z.number().default(1),
	    pos: XYPosition_1.XYPositionSchema,
	    bounds: XYPosition_1.XYPositionSchema.optional(),
	    padding: XYPosition_1.XYPositionSchema.optional(),
	    angle: zod_1.z
	        .number()
	        .nullish()
	        .transform((a) => (typeof a !== 'number' ? undefined : a)),
	    mirror: zod_1.z.tuple([zod_1.z.boolean(), zod_1.z.boolean()]).optional(),
	    justify: zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()]).default([-1, -1]),
	    thickness: zod_1.z.number().default(1),
	    attr: TextAttr_1.TextAttrSchema.array().optional(),
	    ref_designator: zod_1.z.coerce.boolean().optional(),
	    val: zod_1.z.coerce.boolean().optional(),
	    textAlign: zod_1.z.enum(['right', 'left', 'center']).optional(),
	    textTransforms: TextTransform_1.TextTransformSchema.array().optional(),
	    isPositionOffset: zod_1.z.boolean().optional(),
	    rejustifyOnRotate: zod_1.z.boolean().optional(),
	    font: zod_1.z.string().optional(),
	    net: zod_1.z.string().optional(),
	    wordWrap: zod_1.z.boolean().optional(),
	    contrastWithLayer: zod_1.z.boolean().optional(),
	    clipToBounds: zod_1.z.boolean().optional(),
	});
	
	return Text;
}

var hasRequiredModuleDrawProperty;

function requireModuleDrawProperty () {
	if (hasRequiredModuleDrawProperty) return ModuleDrawProperty;
	hasRequiredModuleDrawProperty = 1;
	Object.defineProperty(ModuleDrawProperty, "__esModule", { value: true });
	ModuleDrawProperty.ModuleDrawPropertySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const zod_partial_1 = requireZodPartial();
	const Text_1 = requireText();
	ModuleDrawProperty.ModuleDrawPropertySchema = (0, zod_partial_1.partialWithoutDefaults)(Text_1.TextSchema)
	    .extend({
	    value: zod_1.z.string(),
	    layer: zod_1.z.number().optional(),
	    hidden: zod_1.z.boolean().optional(),
	})
	    .strict();
	
	return ModuleDrawProperty;
}

var ModuleTransform = {};

var hasRequiredModuleTransform;

function requireModuleTransform () {
	if (hasRequiredModuleTransform) return ModuleTransform;
	hasRequiredModuleTransform = 1;
	Object.defineProperty(ModuleTransform, "__esModule", { value: true });
	ModuleTransform.ModuleTransformSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const XYPosition_1 = requireXYPosition();
	ModuleTransform.ModuleTransformSchema = zod_1.z.object({
	    pos: XYPosition_1.XYPositionSchema.optional(),
	    angle: zod_1.z.number().transform((a) => a ?? 0),
	    mirrored: zod_1.z.boolean().array(),
	});
	
	return ModuleTransform;
}

var ProjectDataTypeInstance = {};

var hasRequiredProjectDataTypeInstance;

function requireProjectDataTypeInstance () {
	if (hasRequiredProjectDataTypeInstance) return ProjectDataTypeInstance;
	hasRequiredProjectDataTypeInstance = 1;
	Object.defineProperty(ProjectDataTypeInstance, "__esModule", { value: true });
	ProjectDataTypeInstance.ProjectDataTypeInstance = void 0;
	class ProjectDataTypeInstance$1 {
	    get netRefCtx() {
	        return this.context.netRefCtx;
	    }
	    constructor(context) {
	        this.context = context;
	    }
	}
	ProjectDataTypeInstance.ProjectDataTypeInstance = ProjectDataTypeInstance$1;
	
	return ProjectDataTypeInstance;
}

var hasRequiredModule;

function requireModule () {
	if (hasRequiredModule) return Module;
	hasRequiredModule = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModuleInstance = exports.ModuleSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const zod_partial_1 = requireZodPartial();
		const metadata_1 = requireMetadata();
		const ColorOverride_1 = requireColorOverride();
		const ModuleDrawProperty_1 = requireModuleDrawProperty();
		const ModuleTransform_1 = requireModuleTransform();
		const XYPosition_1 = requireXYPosition();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		exports.ModuleSchema = zod_1.z.object({
		    ref: zod_1.z.string(),
		    footprintRef: zod_1.z.string(),
		    componentRef: zod_1.z.string(),
		    center: XYPosition_1.XYPositionSchema,
		    layer: zod_1.z.enum(['F', 'B']),
		    transform: ModuleTransform_1.ModuleTransformSchema,
		    name: zod_1.z.string(),
		    fullModuleName: zod_1.z.string().optional(),
		    pinNets: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    drawProperties: zod_1.z.record(zod_1.z.string(), ModuleDrawProperty_1.ModuleDrawPropertySchema),
		    metadata: metadata_1.MetadataSchema.optional(),
		    bodyColorOverride: ColorOverride_1.ColorOverrideSchema.optional(),
		    multiInstance: zod_1.z
		        .object({
		        instanceId: zod_1.z.string(),
		        displayModuleRef: zod_1.z.string(),
		    })
		        .optional(),
		    customTypeName: zod_1.z.string().optional(),
		});
		class ModuleInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, rawOpts) {
		        super(context);
		        this.pinNets = new Map();
		        const opts = (0, zod_partial_1.partialWithoutDefaults)(exports.ModuleSchema).parse(rawOpts);
		        this.ref = opts.ref ?? '';
		        this.footprintRef = opts.footprintRef ?? '';
		        this.componentRef = opts.componentRef ?? '';
		        this.center = opts.center ?? [0, 0];
		        this.layer = opts.layer ?? 'F';
		        this.transform = opts.transform ?? { angle: 0, mirrored: [false, false] };
		        this.name = opts.name ?? '';
		        this.fullModuleName = opts.fullModuleName;
		        this.drawProperties = opts.drawProperties ?? {};
		        this.metadata = opts.metadata;
		        this.bodyColorOverride = opts.bodyColorOverride;
		        this.customTypeName = opts.customTypeName;
		        this.pinNets = new Map(Object.entries(opts.pinNets ?? {}).map(([pin, net]) => [pin, context.netRefCtx.existingOrNewRef(net)]));
		    }
		    getPinNet(pin) {
		        return this.pinNets.get(pin);
		    }
		    setPinNet(pin, net) {
		        this.pinNets.set(pin, this.context.netRefCtx.existingOrNewRef(net));
		    }
		    toJSON() {
		        return {
		            ref: this.ref,
		            footprintRef: this.footprintRef,
		            componentRef: this.componentRef,
		            center: this.center,
		            layer: this.layer,
		            transform: this.transform,
		            name: this.name,
		            fullModuleName: this.fullModuleName,
		            drawProperties: structuredClone(this.drawProperties),
		            metadata: this.metadata,
		            bodyColorOverride: this.bodyColorOverride,
		            customTypeName: this.customTypeName,
		            pinNets: Object.fromEntries(Array.from(this.pinNets).map(([pin, net]) => [pin, net.valueOf()])),
		        };
		    }
		}
		exports.ModuleInstance = ModuleInstance;
		
	} (Module));
	return Module;
}

var hasRequiredSchematicReference;

function requireSchematicReference () {
	if (hasRequiredSchematicReference) return SchematicReference;
	hasRequiredSchematicReference = 1;
	Object.defineProperty(SchematicReference, "__esModule", { value: true });
	SchematicReference.SchematicReferenceSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const Module_1 = requireModule();
	SchematicReference.SchematicReferenceSchema = zod_1.z.object({
	    type: zod_1.z.literal('schref'),
	    name: zod_1.z.string(),
	    file: zod_1.z.string(),
	    designId: zod_1.z.string().nullable(),
	    drawModule: Module_1.ModuleSchema,
	});
	
	return SchematicReference;
}

var Bus = {};

var LineStyle = {};

var hasRequiredLineStyle;

function requireLineStyle () {
	if (hasRequiredLineStyle) return LineStyle;
	hasRequiredLineStyle = 1;
	Object.defineProperty(LineStyle, "__esModule", { value: true });
	LineStyle.LineStyleSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	LineStyle.LineStyleSchema = zod_1.z.enum(['solid', 'dash', 'dot', 'dash-dot']);
	
	return LineStyle;
}

var hasRequiredBus;

function requireBus () {
	if (hasRequiredBus) return Bus;
	hasRequiredBus = 1;
	Object.defineProperty(Bus, "__esModule", { value: true });
	Bus.BusSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const LineStyle_1 = requireLineStyle();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Bus.BusSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('bus'),
	    start: XYPosition_1.XYPositionSchema,
	    end: XYPosition_1.XYPositionSchema,
	    width: zod_1.z.number(),
	    style: LineStyle_1.LineStyleSchema.optional(),
	    curve: zod_1.z.number().optional(),
	});
	
	return Bus;
}

var Circle$2 = {};

var hasRequiredCircle;

function requireCircle () {
	if (hasRequiredCircle) return Circle$2;
	hasRequiredCircle = 1;
	Object.defineProperty(Circle$2, "__esModule", { value: true });
	Circle$2.CircleSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Circle$2.CircleSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('circle'),
	    center: XYPosition_1.XYPositionSchema,
	    radius: zod_1.z.number(),
	    filled: zod_1.z.coerce.boolean().optional(),
	    width: zod_1.z.number().optional(),
	});
	
	return Circle$2;
}

var Arc$1 = {};

var hasRequiredArc;

function requireArc () {
	if (hasRequiredArc) return Arc$1;
	hasRequiredArc = 1;
	Object.defineProperty(Arc$1, "__esModule", { value: true });
	Arc$1.ArcSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const LineStyle_1 = requireLineStyle();
	const XYPosition_1 = requireXYPosition();
	Arc$1.ArcSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('arc'),
	    lineWidth: zod_1.z.number(),
	    lineStyle: LineStyle_1.LineStyleSchema.optional(),
	    center: XYPosition_1.XYPositionSchema,
	    radius: zod_1.z.number(),
	    startAngle: zod_1.z.number(),
	    endAngle: zod_1.z.number(),
	});
	
	return Arc$1;
}

var Curve = {};

var hasRequiredCurve;

function requireCurve () {
	if (hasRequiredCurve) return Curve;
	hasRequiredCurve = 1;
	Object.defineProperty(Curve, "__esModule", { value: true });
	Curve.CurveSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Curve.CurveSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('curve'),
	    start: XYPosition_1.XYPositionSchema,
	    end: XYPosition_1.XYPositionSchema,
	    cpa: XYPosition_1.XYPositionSchema,
	    cpb: XYPosition_1.XYPositionSchema,
	    width: zod_1.z.number(),
	});
	
	return Curve;
}

var Polygon$3 = {};

var hasRequiredPolygon$1;

function requirePolygon$1 () {
	if (hasRequiredPolygon$1) return Polygon$3;
	hasRequiredPolygon$1 = 1;
	Object.defineProperty(Polygon$3, "__esModule", { value: true });
	Polygon$3.PolygonSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const LineStyle_1 = requireLineStyle();
	const XYPosition_1 = requireXYPosition();
	Polygon$3.PolygonSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('polygon'),
	    filled: zod_1.z.coerce.boolean().optional(),
	    width: zod_1.z.number(),
	    pos: XYPosition_1.XYPositionSchema.optional(),
	    angle: zod_1.z.number().optional(),
	    points: XYPosition_1.XYPositionSchema.array(),
	    lineStyle: LineStyle_1.LineStyleSchema.optional(),
	});
	
	return Polygon$3;
}

var Pin = {};

var SchDrawingWithLayers = {};

var SchDrawing = {};

var TextBox = {};

var Rect = {};

var hasRequiredRect;

function requireRect () {
	if (hasRequiredRect) return Rect;
	hasRequiredRect = 1;
	Object.defineProperty(Rect, "__esModule", { value: true });
	Rect.RectSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const LineStyle_1 = requireLineStyle();
	const XYPosition_1 = requireXYPosition();
	Rect.RectSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('rect'),
	    start: XYPosition_1.XYPositionSchema,
	    end: XYPosition_1.XYPositionSchema,
	    strokeWidth: zod_1.z.number(),
	    filled: zod_1.z.coerce.boolean().optional(),
	    lineStyle: LineStyle_1.LineStyleSchema.optional(),
	});
	
	return Rect;
}

var hasRequiredTextBox;

function requireTextBox () {
	if (hasRequiredTextBox) return TextBox;
	hasRequiredTextBox = 1;
	Object.defineProperty(TextBox, "__esModule", { value: true });
	TextBox.TextBoxSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const Rect_1 = requireRect();
	const Text_1 = requireText();
	TextBox.TextBoxSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('textbox'),
	    text: Text_1.TextSchema,
	    backgroundLayer: zod_1.z.number(),
	    background: Rect_1.RectSchema.omit({ start: true, end: true }),
	});
	
	return TextBox;
}

var DrawingGroup = {};

var SchGroupDrawableTypes = {};

var Junction = {};

var hasRequiredJunction;

function requireJunction () {
	if (hasRequiredJunction) return Junction;
	hasRequiredJunction = 1;
	Object.defineProperty(Junction, "__esModule", { value: true });
	Junction.JunctionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Junction.JunctionSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('junction'),
	    position: XYPosition_1.XYPositionSchema,
	    radiusOverride: zod_1.z.number().optional(),
	    net: zod_1.z.coerce.string().optional(),
	});
	
	return Junction;
}

var Label$1 = {};

var hasRequiredLabel$1;

function requireLabel$1 () {
	if (hasRequiredLabel$1) return Label$1;
	hasRequiredLabel$1 = 1;
	Object.defineProperty(Label$1, "__esModule", { value: true });
	Label$1.LabelSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Label$1.LabelSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('label'),
	    pos: XYPosition_1.XYPositionSchema,
	    orientation: zod_1.z.enum(['L', 'D', 'R', 'U']),
	    shape: zod_1.z.enum(['input', 'output', 'bidirectional', 'neutral']),
	    value: zod_1.z.string(),
	    net: zod_1.z.coerce.string().optional(),
	    filled: zod_1.z.coerce.boolean().optional(),
	});
	
	return Label$1;
}

var Segment$2 = {};

var hasRequiredSegment;

function requireSegment () {
	if (hasRequiredSegment) return Segment$2;
	hasRequiredSegment = 1;
	Object.defineProperty(Segment$2, "__esModule", { value: true });
	Segment$2.SegmentSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const LineStyle_1 = requireLineStyle();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Segment$2.SegmentSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('segment'),
	    start: XYPosition_1.XYPositionSchema,
	    end: XYPosition_1.XYPositionSchema,
	    width: zod_1.z.number(),
	    style: LineStyle_1.LineStyleSchema.optional(),
	    curve: zod_1.z.number().optional(),
	});
	
	return Segment$2;
}

var Track = {};

var hasRequiredTrack;

function requireTrack () {
	if (hasRequiredTrack) return Track;
	hasRequiredTrack = 1;
	Object.defineProperty(Track, "__esModule", { value: true });
	Track.TrackSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Track.TrackSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('track'),
	    layer: zod_1.z.number().optional(),
	    start: XYPosition_1.XYPositionSchema,
	    end: XYPosition_1.XYPositionSchema,
	    center: XYPosition_1.XYPositionSchema.optional(),
	    radius: zod_1.z.number().optional(),
	    startangle: zod_1.z.number().optional(),
	    endangle: zod_1.z.number().optional(),
	    width: zod_1.z.number(),
	    net: zod_1.z.string().optional(),
	});
	
	return Track;
}

var Image = {};

var hasRequiredImage;

function requireImage () {
	if (hasRequiredImage) return Image;
	hasRequiredImage = 1;
	Object.defineProperty(Image, "__esModule", { value: true });
	Image.ImageSchema = Image.ImageMimeTypeSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Image.ImageMimeTypeSchema = zod_1.z.enum(['image/png', 'image/jpeg', 'image/bmp', 'application/octet-stream']);
	Image.ImageSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('image'),
	    pos: XYPosition_1.XYPositionSchema,
	    width: zod_1.z.number(),
	    height: zod_1.z.number(),
	    angle: zod_1.z.number().optional(),
	    imageRef: zod_1.z.string(),
	});
	
	return Image;
}

var hasRequiredSchGroupDrawableTypes;

function requireSchGroupDrawableTypes () {
	if (hasRequiredSchGroupDrawableTypes) return SchGroupDrawableTypes;
	hasRequiredSchGroupDrawableTypes = 1;
	Object.defineProperty(SchGroupDrawableTypes, "__esModule", { value: true });
	SchGroupDrawableTypes.SchGroupDrawableTypesSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const Bus_1 = requireBus();
	const Circle_1 = requireCircle();
	const Polygon_1 = requirePolygon$1();
	const TextBox_1 = requireTextBox();
	const Junction_1 = requireJunction();
	const Label_1 = requireLabel$1();
	const Rect_1 = requireRect();
	const Segment_1 = requireSegment();
	const Text_1 = requireText();
	const Track_1 = requireTrack();
	const Image_1 = requireImage();
	SchGroupDrawableTypes.SchGroupDrawableTypesSchema = zod_1.z.discriminatedUnion('type', [
	    Bus_1.BusSchema,
	    Circle_1.CircleSchema,
	    Image_1.ImageSchema,
	    Junction_1.JunctionSchema,
	    Label_1.LabelSchema,
	    Polygon_1.PolygonSchema,
	    Segment_1.SegmentSchema,
	    Rect_1.RectSchema,
	    Text_1.TextSchema,
	    Track_1.TrackSchema,
	    TextBox_1.TextBoxSchema,
	]);
	
	return SchGroupDrawableTypes;
}

var hasRequiredDrawingGroup;

function requireDrawingGroup () {
	if (hasRequiredDrawingGroup) return DrawingGroup;
	hasRequiredDrawingGroup = 1;
	Object.defineProperty(DrawingGroup, "__esModule", { value: true });
	DrawingGroup.DrawingGroupSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const SchGroupDrawableTypes_1 = requireSchGroupDrawableTypes();
	const XYPosition_1 = requireXYPosition();
	DrawingGroup.DrawingGroupSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('group'),
	    pos: XYPosition_1.XYPositionSchema,
	    angle: zod_1.z.number().optional(),
	    drawings: SchGroupDrawableTypes_1.SchGroupDrawableTypesSchema.array(),
	    net: zod_1.z.string().optional(),
	});
	
	return DrawingGroup;
}

var RichText = {};

var hasRequiredRichText;

function requireRichText () {
	if (hasRequiredRichText) return RichText;
	hasRequiredRichText = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RichTextSchema = exports.RichTextParagraphSchema = exports.RichTextRunSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ColorOverride_1 = requireColorOverride();
		const DrawingBase_1 = requireDrawingBase();
		const XYPosition_1 = requireXYPosition();
		exports.RichTextRunSchema = zod_1.z.object({
		    value: zod_1.z.string(),
		    bold: zod_1.z.boolean().optional(),
		    italic: zod_1.z.boolean().optional(),
		    sizePt: zod_1.z.number().optional(),
		    color: ColorOverride_1.ColorOverrideSchema.optional(),
		    font: zod_1.z.string().optional(),
		});
		exports.RichTextParagraphSchema = zod_1.z.object({
		    runs: zod_1.z.array(exports.RichTextRunSchema),
		});
		exports.RichTextSchema = DrawingBase_1.DrawingBaseSchema.extend({
		    type: zod_1.z.literal('rich-text'),
		    pos: XYPosition_1.XYPositionSchema,
		    width: zod_1.z.number(),
		    height: zod_1.z.number(),
		    paragraphs: zod_1.z.array(exports.RichTextParagraphSchema),
		    defaultSizePt: zod_1.z.number().optional(),
		    defaultBold: zod_1.z.boolean().optional(),
		    defaultItalic: zod_1.z.boolean().optional(),
		    defaultFont: zod_1.z.string().optional(),
		    defaultColor: ColorOverride_1.ColorOverrideSchema.optional(),
		});
		
	} (RichText));
	return RichText;
}

var hasRequiredSchDrawing;

function requireSchDrawing () {
	if (hasRequiredSchDrawing) return SchDrawing;
	hasRequiredSchDrawing = 1;
	Object.defineProperty(SchDrawing, "__esModule", { value: true });
	SchDrawing.SchDrawingSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SchematicReference_1 = requireSchematicReference();
	const Bus_1 = requireBus();
	const Circle_1 = requireCircle();
	const Arc_1 = requireArc();
	const Curve_1 = requireCurve();
	const Polygon_1 = requirePolygon$1();
	const TextBox_1 = requireTextBox();
	const DrawingGroup_1 = requireDrawingGroup();
	const Junction_1 = requireJunction();
	const Label_1 = requireLabel$1();
	const Rect_1 = requireRect();
	const Segment_1 = requireSegment();
	const Text_1 = requireText();
	const Track_1 = requireTrack();
	const Image_1 = requireImage();
	const RichText_1 = requireRichText();
	SchDrawing.SchDrawingSchema = zod_1.z.discriminatedUnion('type', [
	    Arc_1.ArcSchema,
	    Curve_1.CurveSchema,
	    Bus_1.BusSchema,
	    Circle_1.CircleSchema,
	    Image_1.ImageSchema,
	    Junction_1.JunctionSchema,
	    Label_1.LabelSchema,
	    Polygon_1.PolygonSchema,
	    Segment_1.SegmentSchema,
	    Rect_1.RectSchema,
	    RichText_1.RichTextSchema,
	    Text_1.TextSchema,
	    Track_1.TrackSchema,
	    SchematicReference_1.SchematicReferenceSchema,
	    DrawingGroup_1.DrawingGroupSchema,
	    TextBox_1.TextBoxSchema,
	]);
	
	return SchDrawing;
}

var hasRequiredSchDrawingWithLayers;

function requireSchDrawingWithLayers () {
	if (hasRequiredSchDrawingWithLayers) return SchDrawingWithLayers;
	hasRequiredSchDrawingWithLayers = 1;
	Object.defineProperty(SchDrawingWithLayers, "__esModule", { value: true });
	SchDrawingWithLayers.SchDrawingWithLayersSchema = void 0;
	const DrawingWithLayers_1 = requireDrawingWithLayers();
	const SchDrawing_1 = requireSchDrawing();
	SchDrawingWithLayers.SchDrawingWithLayersSchema = (0, DrawingWithLayers_1.DrawingWithLayersSchemaT)(SchDrawing_1.SchDrawingSchema);
	
	return SchDrawingWithLayers;
}

var hasRequiredPin;

function requirePin () {
	if (hasRequiredPin) return Pin;
	hasRequiredPin = 1;
	Object.defineProperty(Pin, "__esModule", { value: true });
	Pin.PinSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const zod_field_presence_1 = requireZodFieldPresence();
	const SchDrawingWithLayers_1 = requireSchDrawingWithLayers();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Pin.PinSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('pin'),
	    ref: zod_1.z.string(),
	    name: zod_1.z.string(),
	    number: (0, zod_field_presence_1.coercedString)(),
	    start: XYPosition_1.XYPositionSchema,
	    end: XYPosition_1.XYPositionSchema,
	    layer: zod_1.z.number(),
	    isNetLabel: zod_1.z.coerce.boolean().optional(),
	    hidden: zod_1.z.coerce.boolean().optional(),
	    drawings: zod_1.z.lazy(() => SchDrawingWithLayers_1.SchDrawingWithLayersSchema.array().optional()),
	});
	
	return Pin;
}

var Pad = {};

var hasRequiredPad;

function requirePad () {
	if (hasRequiredPad) return Pad;
	hasRequiredPad = 1;
	Object.defineProperty(Pad, "__esModule", { value: true });
	Pad.PadSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingBase_1 = requireDrawingBase();
	const XYPosition_1 = requireXYPosition();
	Pad.PadSchema = DrawingBase_1.DrawingBaseSchema.extend({
	    type: zod_1.z.literal('pad'),
	    ref: zod_1.z.string(),
	    name: zod_1.z.string(),
	    pos: XYPosition_1.XYPositionSchema,
	    diameter: zod_1.z.number().array(),
	    angle: zod_1.z
	        .number()
	        .nullish()
	        .transform((a) => a ?? 0),
	    layers: zod_1.z.number().array().optional(),
	    pin1: zod_1.z.coerce.boolean().optional(),
	    shape: zod_1.z.enum(['rect', 'oval', 'circle', 'roundrect', 'custom']),
	    polygons: XYPosition_1.XYPositionSchema.array().array().optional(),
	    radius: zod_1.z.number().optional(),
	    chamfpos: zod_1.z.number().optional(),
	    chamfratio: zod_1.z.number().optional(),
	    padType: zod_1.z.enum(['th', 'smd']),
	    drillshape: zod_1.z.enum(['circle', 'oblong']).optional(),
	    drillDiameter: zod_1.z.number().array().optional(),
	    offset: XYPosition_1.XYPositionSchema.optional(),
	});
	
	return Pad;
}

var hasRequiredAnyDrawingSchema;

function requireAnyDrawingSchema () {
	if (hasRequiredAnyDrawingSchema) return AnyDrawingSchema;
	hasRequiredAnyDrawingSchema = 1;
	Object.defineProperty(AnyDrawingSchema, "__esModule", { value: true });
	AnyDrawingSchema.AnyDrawingSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SchematicReference_1 = requireSchematicReference();
	const Bus_1 = requireBus();
	const Circle_1 = requireCircle();
	const Arc_1 = requireArc();
	const Curve_1 = requireCurve();
	const Polygon_1 = requirePolygon$1();
	const Pin_1 = requirePin();
	const Pad_1 = requirePad();
	const TextBox_1 = requireTextBox();
	const DrawingGroup_1 = requireDrawingGroup();
	const Junction_1 = requireJunction();
	const Label_1 = requireLabel$1();
	const Rect_1 = requireRect();
	const Segment_1 = requireSegment();
	const Text_1 = requireText();
	const Track_1 = requireTrack();
	const Image_1 = requireImage();
	AnyDrawingSchema.AnyDrawingSchema = zod_1.z.discriminatedUnion('type', [
	    Arc_1.ArcSchema,
	    Curve_1.CurveSchema,
	    Bus_1.BusSchema,
	    Circle_1.CircleSchema,
	    Image_1.ImageSchema,
	    Junction_1.JunctionSchema,
	    Label_1.LabelSchema,
	    Polygon_1.PolygonSchema,
	    Segment_1.SegmentSchema,
	    Rect_1.RectSchema,
	    Text_1.TextSchema,
	    Track_1.TrackSchema,
	    SchematicReference_1.SchematicReferenceSchema,
	    DrawingGroup_1.DrawingGroupSchema,
	    TextBox_1.TextBoxSchema,
	    Pad_1.PadSchema,
	    Pin_1.PinSchema,
	]);
	
	return AnyDrawingSchema;
}

var hasRequiredDrawingWithLayers;

function requireDrawingWithLayers () {
	if (hasRequiredDrawingWithLayers) return DrawingWithLayers;
	hasRequiredDrawingWithLayers = 1;
	Object.defineProperty(DrawingWithLayers, "__esModule", { value: true });
	DrawingWithLayers.AnyDrawingWithLayersSchema = void 0;
	DrawingWithLayers.DrawingWithLayersSchemaT = DrawingWithLayersSchemaT;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnyDrawingSchema_1 = requireAnyDrawingSchema();
	function DrawingWithLayersSchemaT(drawingSchema) {
	    return zod_1.z.object({
	        layers: zod_1.z.number().array(),
	        drawing: drawingSchema,
	    });
	}
	DrawingWithLayers.AnyDrawingWithLayersSchema = zod_1.z.object({
	    layers: zod_1.z.number().array(),
	    drawing: AnyDrawingSchema_1.AnyDrawingSchema,
	});
	
	return DrawingWithLayers;
}

var hasRequiredFootprint;

function requireFootprint () {
	if (hasRequiredFootprint) return Footprint;
	hasRequiredFootprint = 1;
	Object.defineProperty(Footprint, "__esModule", { value: true });
	Footprint.FootprintSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const DrawingWithLayers_1 = requireDrawingWithLayers();
	Footprint.FootprintSchema = zod_1.z.object({
	    ref: zod_1.z.string(),
	    name: zod_1.z.string(),
	    drawings: zod_1.z.lazy(() => DrawingWithLayers_1.AnyDrawingWithLayersSchema.array()),
	    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional(),
	});
	
	return Footprint;
}

var hasRequiredSchFootprint;

function requireSchFootprint () {
	if (hasRequiredSchFootprint) return SchFootprint;
	hasRequiredSchFootprint = 1;
	Object.defineProperty(SchFootprint, "__esModule", { value: true });
	SchFootprint.SchFootprintSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const Footprint_1 = requireFootprint();
	const Pin_1 = requirePin();
	SchFootprint.SchFootprintSchema = Footprint_1.FootprintSchema.extend({
	    pins: zod_1.z.lazy(() => Pin_1.PinSchema.array()),
	});
	
	return SchFootprint;
}

var PcbFootprint = {};

var hasRequiredPcbFootprint;

function requirePcbFootprint () {
	if (hasRequiredPcbFootprint) return PcbFootprint;
	hasRequiredPcbFootprint = 1;
	Object.defineProperty(PcbFootprint, "__esModule", { value: true });
	PcbFootprint.PcbFootprintSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const Footprint_1 = requireFootprint();
	const Pad_1 = requirePad();
	PcbFootprint.PcbFootprintSchema = Footprint_1.FootprintSchema.extend({
	    pads: zod_1.z.lazy(() => Pad_1.PadSchema.array()),
	});
	
	return PcbFootprint;
}

var Parametrics$1 = {};

var ParameterValue = {};

var hasRequiredParameterValue;

function requireParameterValue () {
	if (hasRequiredParameterValue) return ParameterValue;
	hasRequiredParameterValue = 1;
	Object.defineProperty(ParameterValue, "__esModule", { value: true });
	ParameterValue.ParameterValueSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	ParameterValue.ParameterValueSchema = zod_1.z.object({
	    units: zod_1.z.string(),
	    value: zod_1.z.union([zod_1.z.boolean(), zod_1.z.number(), zod_1.z.string(), zod_1.z.number().array(), zod_1.z.string().array()]),
	});
	
	return ParameterValue;
}

var hasRequiredParametrics$1;

function requireParametrics$1 () {
	if (hasRequiredParametrics$1) return Parametrics$1;
	hasRequiredParametrics$1 = 1;
	Object.defineProperty(Parametrics$1, "__esModule", { value: true });
	Parametrics$1.ParametricsSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ParameterValue_1 = requireParameterValue();
	Parametrics$1.ParametricsSchema = zod_1.z
	    .object({
	    supplyVoltage: ParameterValue_1.ParameterValueSchema.optional(),
	    maxCurrentConsumption: ParameterValue_1.ParameterValueSchema.optional(),
	    radioFrequency: ParameterValue_1.ParameterValueSchema.optional(),
	    signalLevel: ParameterValue_1.ParameterValueSchema.optional(),
	    maxCurrent: ParameterValue_1.ParameterValueSchema.optional(),
	    isFloating: ParameterValue_1.ParameterValueSchema.optional(),
	    isActiveLow: ParameterValue_1.ParameterValueSchema.optional(),
	    fetDrive: ParameterValue_1.ParameterValueSchema.optional(),
	    pinPullup: ParameterValue_1.ParameterValueSchema.optional(),
	    pullupResistance: ParameterValue_1.ParameterValueSchema.optional(),
	    pullupVoltageSignal: ParameterValue_1.ParameterValueSchema.optional(),
	    differentialSignal: ParameterValue_1.ParameterValueSchema.optional(),
	    differentialPolarity: ParameterValue_1.ParameterValueSchema.optional(),
	    clampUpSignal: ParameterValue_1.ParameterValueSchema.optional(),
	    clampDownSignal: ParameterValue_1.ParameterValueSchema.optional(),
	})
	    .catchall(ParameterValue_1.ParameterValueSchema.optional());
	
	return Parametrics$1;
}

var StandardComponentModel = {};

var hasRequiredStandardComponentModel;

function requireStandardComponentModel () {
	if (hasRequiredStandardComponentModel) return StandardComponentModel;
	hasRequiredStandardComponentModel = 1;
	Object.defineProperty(StandardComponentModel, "__esModule", { value: true });
	StandardComponentModel.StandardComponentModelSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const InferenceReports_1 = requireInferenceReports();
	const SPICE_1 = requireSPICE();
	StandardComponentModel.StandardComponentModelSchema = zod_1.z.object({
	    modelId: zod_1.z.string(),
	    isPassive: zod_1.z.boolean().optional(),
	    parameterInferenceReport: zod_1.z.any().optional(),
	    inferenceReport: InferenceReports_1.InferenceReportSchema.optional(),
	    models: SPICE_1.ISubModelOptsSchema.array().optional(),
	});
	
	return StandardComponentModel;
}

var hasRequiredComponent;

function requireComponent () {
	if (hasRequiredComponent) return component;
	hasRequiredComponent = 1;
	Object.defineProperty(component, "__esModule", { value: true });
	component.ComponentSchema = void 0;
	component.migrateComponent = migrateComponent;
	component.migrateSignal = migrateSignal;
	component.migrateLegacyParameters = migrateLegacyParameters;
	const BoundaryLevel_1 = requireBoundaryLevel();
	const SchFootprint_1 = requireSchFootprint();
	const PcbFootprint_1 = requirePcbFootprint();
	const Migration_1 = requireMigration();
	const MergeInferenceReports_1 = requireMergeInferenceReports();
	const zod_1 = /*@__PURE__*/ requireZod();
	const metadata_1 = requireMetadata();
	const Signal_1 = requireSignal();
	const Parametrics_1 = requireParametrics$1();
	const StandardComponentModel_1 = requireStandardComponentModel();
	component.ComponentSchema = zod_1.z.object({
	    name: zod_1.z.string(),
	    numPins: zod_1.z.union([zod_1.z.number(), zod_1.z.number().array()]),
	    numPads: zod_1.z.union([zod_1.z.number(), zod_1.z.number().array()]),
	    metadata: metadata_1.MetadataSchema.optional(),
	    parametrics: Parametrics_1.ParametricsSchema.optional(),
	    footprints: PcbFootprint_1.PcbFootprintSchema.array(),
	    symbols: SchFootprint_1.SchFootprintSchema.array(),
	    signals: Signal_1.SignalSchema.array(),
	    model: StandardComponentModel_1.StandardComponentModelSchema.optional(),
	    isVirtual: zod_1.z.boolean().optional(),
	    isMultiUnit: zod_1.z.boolean().optional(),
	});
	function migrateComponent(component) {
	    const result = {
	        type: Migration_1.MigrationResultType.ComponentMigration,
	        isModified: false,
	        entity: structuredClone(component),
	        message: '',
	        innerResults: [],
	    };
	    const signalsMigrationResult = migrateComponentSignals(component);
	    if (signalsMigrationResult.isModified) {
	        result.innerResults.push(signalsMigrationResult);
	        result.isModified = true;
	        result.entity.signals = component.signals.map((signal) => {
	            const migratedSignal = signalsMigrationResult.entity.find((migratedSignal) => migratedSignal.id === signal.id);
	            return migratedSignal ?? signal;
	        });
	    }
	    if (result.isModified) {
	        result.message = `${result.entity.name}: Migrated ${result.innerResults.length} properties`;
	    }
	    if (result.entity.model?.parameterInferenceReport) {
	        result.entity.model.inferenceReport = (0, MergeInferenceReports_1.mergeInferenceReports)(result.entity.model.parameterInferenceReport, result.entity.model.inferenceReport);
	        delete result.entity.model.parameterInferenceReport;
	    }
	    if (result.entity.model && 'parameters' in result.entity.model) {
	        result.entity.model.inferenceReport = (0, MergeInferenceReports_1.mergeInferenceReports)(result.entity.model.inferenceReport, migrateLegacyParameters(result.entity.model.parameters));
	        result.innerResults?.push({
	            type: Migration_1.MigrationResultType.PropertyModified,
	            message: 'Migrated legacy parameters to inference report',
	            severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	        });
	        delete result.entity.model.parameters;
	        result.isModified = true;
	    }
	    return result;
	}
	function migrateComponentSignals(component) {
	    const migratedSignals = component.signals
	        .map((signal) => migrateSignal(signal))
	        .filter((signal) => signal.isModified);
	    const result = {
	        type: Migration_1.MigrationResultType.PropertyModified,
	        isModified: migratedSignals.length > 0,
	        entity: migratedSignals.map((signal) => signal.entity),
	        message: `Migrated ${migratedSignals.length} signals`,
	        innerResults: migratedSignals,
	    };
	    return result;
	}
	function migrateSignal(signal) {
	    const result = {
	        type: Migration_1.MigrationResultType.SignalMigration,
	        isModified: false,
	        entity: structuredClone(signal),
	        message: '',
	        innerResults: [],
	    };
	    if (!signal.attributes) {
	        result.entity.attributes = {};
	        result.innerResults.push({
	            type: Migration_1.MigrationResultType.PropertyAdded,
	            message: 'Added attributes property',
	            severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	        });
	    }
	    if (signal.signalType && ['input', 'output', 'biio'].includes(signal.signalType.toString())) {
	        result.entity.signalDirection = signal.signalType.toString();
	        result.innerResults.push({
	            type: Migration_1.MigrationResultType.PropertyModified,
	            message: `Migrated legacy signalType to signalDirection for value: ${signal.signalType.toString()}`,
	            severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	        });
	        delete result.entity.signalType;
	        result.innerResults.push({
	            type: Migration_1.MigrationResultType.PropertyDeleted,
	            message: 'Removed deprecated signalType property',
	            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	        });
	        result.isModified = true;
	    }
	    if (Object.prototype.hasOwnProperty.call(signal, 'powerSignalType')) {
	        const powerSignalType = signal['powerSignalType']?.toString() ?? '';
	        if (powerSignalType.includes('gnd')) {
	            result.entity.attributes.ground = true;
	            result.innerResults.push({
	                type: Migration_1.MigrationResultType.PropertyModified,
	                message: `Migrated legacy powerSignalType '${powerSignalType}' to SignalAttribute ground`,
	                severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	            });
	        }
	        else {
	            result.entity.attributes.power = true;
	            result.innerResults.push({
	                type: Migration_1.MigrationResultType.PropertyModified,
	                message: `Migrated legacy powerSignalType '${powerSignalType}' to SignalAttribute power`,
	                severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	            });
	        }
	        delete result.entity.powerSignalType;
	        result.innerResults.push({
	            type: Migration_1.MigrationResultType.PropertyDeleted,
	            message: 'Removed deprecated powerSignalType property',
	            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	        });
	        result.isModified = true;
	    }
	    if (signal.signalType && ['power'].includes(signal.signalType?.toString())) {
	        if (!(result.entity.attributes?.ground || result.entity.attributes?.power)) {
	            if (/gnd|ground/i.test(signal.name)) {
	                result.entity.attributes.ground = true;
	                result.innerResults.push({
	                    type: Migration_1.MigrationResultType.PropertyModified,
	                    message: `Migrated legacy signalType 'power' to SignalAttribute ground`,
	                    severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	                });
	            }
	            else {
	                result.entity.attributes.power = true;
	                result.innerResults.push({
	                    type: Migration_1.MigrationResultType.PropertyModified,
	                    message: `Insufficient information to migrate legacy signalType 'power' to SignalAttribute power or ground`,
	                    severity: BoundaryLevel_1.BoundaryLevel.Error,
	                });
	            }
	        }
	        delete result.entity.signalType;
	        result.innerResults.push({
	            type: Migration_1.MigrationResultType.PropertyDeleted,
	            message: 'Removed deprecated signalType property',
	            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	        });
	        result.isModified = true;
	    }
	    if (result.isModified) {
	        result.message = `Migrated signal: ${signal.name}`;
	    }
	    return result;
	}
	function migrateLegacyParameters(parameters) {
	    return {
	        parameters: Object.entries(parameters).reduce((acc, [key, value]) => {
	            if (value === undefined || value === null) {
	                return acc;
	            }
	            if (typeof value === 'object') {
	                if ((value.value === undefined || value.value === null || value.value === '') &&
	                    (value.variance === undefined || value.variance === null || value.variance === '')) {
	                    return acc;
	                }
	            }
	            acc[key] = {
	                source: 'hil',
	                value,
	            };
	            return acc;
	        }, {}),
	    };
	}
	
	return component;
}

var ComponentDefinition = {};

var hasRequiredComponentDefinition;

function requireComponentDefinition () {
	if (hasRequiredComponentDefinition) return ComponentDefinition;
	hasRequiredComponentDefinition = 1;
	Object.defineProperty(ComponentDefinition, "__esModule", { value: true });
	ComponentDefinition.ComponentDefinitionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const component_1 = requireComponent();
	ComponentDefinition.ComponentDefinitionSchema = zod_1.z.object({
	    component: component_1.ComponentSchema,
	});
	
	return ComponentDefinition;
}

var ComponentDefinitionWithBoundaries = {};

var ComponentWithBoundaries = {};

var hasRequiredComponentWithBoundaries;

function requireComponentWithBoundaries () {
	if (hasRequiredComponentWithBoundaries) return ComponentWithBoundaries;
	hasRequiredComponentWithBoundaries = 1;
	Object.defineProperty(ComponentWithBoundaries, "__esModule", { value: true });
	ComponentWithBoundaries.ComponentWithBoundariesSchema = void 0;
	ComponentWithBoundaries.migrateComponentWithBoundaries = migrateComponentWithBoundaries;
	const ComponentBoundary_1 = requireComponentBoundary();
	const ModeBase_1 = requireModeBase();
	const FunctionalModels_1 = requireFunctionalModels();
	const component_1 = requireComponent();
	const IComponentFunction_1 = requireIComponentFunction();
	ComponentWithBoundaries.ComponentWithBoundariesSchema = component_1.ComponentSchema.extend({
	    modes: ModeBase_1.ModeBaseSchema.array(),
	    boundaries: ComponentBoundary_1.ComponentBoundarySchema.array(),
	    resources: FunctionalModels_1.IComponentResourceSchema.array().optional(),
	    functions: IComponentFunction_1.IComponentFunctionSchema.array().optional(),
	});
	function migrateComponentWithBoundaries(component) {
	    const migratedComponent = (0, component_1.migrateComponent)(component);
	    return {
	        ...migratedComponent,
	        entity: { ...migratedComponent.entity, boundaries: component.boundaries, modes: component.modes },
	    };
	}
	
	return ComponentWithBoundaries;
}

var hasRequiredComponentDefinitionWithBoundaries;

function requireComponentDefinitionWithBoundaries () {
	if (hasRequiredComponentDefinitionWithBoundaries) return ComponentDefinitionWithBoundaries;
	hasRequiredComponentDefinitionWithBoundaries = 1;
	Object.defineProperty(ComponentDefinitionWithBoundaries, "__esModule", { value: true });
	ComponentDefinitionWithBoundaries.ComponentDefinitionWithBoundariesSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ComponentWithBoundaries_1 = requireComponentWithBoundaries();
	ComponentDefinitionWithBoundaries.ComponentDefinitionWithBoundariesSchema = zod_1.z.object({
	    component: ComponentWithBoundaries_1.ComponentWithBoundariesSchema,
	});
	
	return ComponentDefinitionWithBoundaries;
}

var ComponentMergeUtils = {};

var hasRequiredComponentMergeUtils;

function requireComponentMergeUtils () {
	if (hasRequiredComponentMergeUtils) return ComponentMergeUtils;
	hasRequiredComponentMergeUtils = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DEFINITION_PRIORITY = void 0;
		exports.mergeBoundaries = mergeBoundaries;
		exports.mergeResources = mergeResources;
		exports.mergeFunctions = mergeFunctions;
		exports.DEFINITION_PRIORITY = 1;
		function mergeBoundaries(definitionBoundaries, inferenceReportBoundaries) {
		    const inferenceReportBoundaryValues = inferenceReportBoundaries?.map((entry) => entry.boundary) ?? [];
		    const topLevelBoundaries = definitionBoundaries ?? [];
		    return [...inferenceReportBoundaryValues, ...topLevelBoundaries];
		}
		function mergeResources(definitionResources, inferenceReportResources) {
		    const allResourcesWithPriority = [
		        ...(definitionResources ?? []).map((r) => ({ priority: exports.DEFINITION_PRIORITY, resource: r })),
		        ...(inferenceReportResources ?? []).map((r) => ({
		            priority: r.priority ?? 0,
		            resource: r.resource,
		        })),
		    ].sort((a, b) => a.priority - b.priority);
		    const resourceMap = new Map();
		    for (const { resource } of allResourcesWithPriority) {
		        const key = resource.name;
		        resourceMap.set(key, resource);
		    }
		    return Array.from(resourceMap.values());
		}
		function mergeFunctions(definitionFunctions, inferenceReportFunctions) {
		    const allFunctionsWithPriority = [
		        ...(definitionFunctions ?? []).map((f) => ({ priority: exports.DEFINITION_PRIORITY, function: f })),
		        ...(inferenceReportFunctions ?? []).map((f) => ({
		            priority: f.priority ?? 0,
		            function: f.function,
		        })),
		    ].sort((a, b) => a.priority - b.priority);
		    const functionMap = new Map();
		    for (const { function: func } of allFunctionsWithPriority) {
		        functionMap.set(func.name, func);
		    }
		    return Array.from(functionMap.values());
		}
		
	} (ComponentMergeUtils));
	return ComponentMergeUtils;
}

var CoreModel = {};

var hasRequiredCoreModel;

function requireCoreModel () {
	if (hasRequiredCoreModel) return CoreModel;
	hasRequiredCoreModel = 1;
	Object.defineProperty(CoreModel, "__esModule", { value: true });
	
	return CoreModel;
}

var Design = {};

var pcb = {};

var PcbDrawing = {};

var Via = {};

var hasRequiredVia;

function requireVia () {
	if (hasRequiredVia) return Via;
	hasRequiredVia = 1;
	Object.defineProperty(Via, "__esModule", { value: true });
	Via.ViaSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const XYPosition_1 = requireXYPosition();
	Via.ViaSchema = zod_1.z.object({
	    type: zod_1.z.literal('via'),
	    center: XYPosition_1.XYPositionSchema,
	    diameter: zod_1.z.number(),
	    drillDiameter: zod_1.z.number(),
	    layers: zod_1.z.number().array(),
	    net: zod_1.z.string(),
	    shape: zod_1.z.enum(['rect', 'circle', 'custom']),
	    polygons: zod_1.z.number().array().array().array().optional(),
	});
	
	return Via;
}

var Zone = {};

var hasRequiredZone;

function requireZone () {
	if (hasRequiredZone) return Zone;
	hasRequiredZone = 1;
	Object.defineProperty(Zone, "__esModule", { value: true });
	Zone.ZoneSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const XYPosition_1 = requireXYPosition();
	Zone.ZoneSchema = zod_1.z.object({
	    type: zod_1.z.literal('zone'),
	    layer: zod_1.z.number(),
	    polygons: zod_1.z
	        .object({
	        points: XYPosition_1.XYPositionSchema.array(),
	        filled: zod_1.z.coerce.boolean().optional(),
	        lineWidth: zod_1.z.number().optional(),
	    })
	        .array(),
	    net: zod_1.z.string(),
	});
	
	return Zone;
}

var hasRequiredPcbDrawing;

function requirePcbDrawing () {
	if (hasRequiredPcbDrawing) return PcbDrawing;
	hasRequiredPcbDrawing = 1;
	Object.defineProperty(PcbDrawing, "__esModule", { value: true });
	PcbDrawing.PcbDrawingSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const Circle_1 = requireCircle();
	const Arc_1 = requireArc();
	const Curve_1 = requireCurve();
	const Polygon_1 = requirePolygon$1();
	const Pad_1 = requirePad();
	const TextBox_1 = requireTextBox();
	const Rect_1 = requireRect();
	const Segment_1 = requireSegment();
	const Text_1 = requireText();
	const Track_1 = requireTrack();
	const Via_1 = requireVia();
	const Zone_1 = requireZone();
	PcbDrawing.PcbDrawingSchema = zod_1.z.discriminatedUnion('type', [
	    Arc_1.ArcSchema,
	    Circle_1.CircleSchema,
	    Curve_1.CurveSchema,
	    Pad_1.PadSchema,
	    Polygon_1.PolygonSchema,
	    Rect_1.RectSchema,
	    Segment_1.SegmentSchema,
	    TextBox_1.TextBoxSchema,
	    Text_1.TextSchema,
	    Track_1.TrackSchema,
	    Via_1.ViaSchema,
	    Zone_1.ZoneSchema,
	]);
	
	return PcbDrawing;
}

var ModelBase = {};

var DesignFileNetInfo = {};

var NetInfo = {};

var nets = {};

var hasRequiredNets;

function requireNets () {
	if (hasRequiredNets) return nets;
	hasRequiredNets = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetConnectionInstance = exports.NetConnectionWithNetSchema = exports.NetConnectionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		exports.NetConnectionSchema = zod_1.z.object({
		    moduleRef: zod_1.z.string(),
		    moduleName: zod_1.z.string(),
		    pinRef: zod_1.z.string(),
		    pinFunction: zod_1.z.string().optional(),
		});
		exports.NetConnectionWithNetSchema = exports.NetConnectionSchema.extend({
		    netName: zod_1.z.string(),
		});
		class NetConnectionInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, opts) {
		        super(context);
		        this.moduleRef = opts.moduleRef;
		        this.moduleName = opts.moduleName;
		        this.pinRef = opts.pinRef;
		        this.pinFunction = opts.pinFunction;
		    }
		    toJSON() {
		        return {
		            moduleRef: this.moduleRef,
		            moduleName: this.moduleName,
		            pinRef: this.pinRef,
		            ...(this.pinFunction ? { pinFunction: this.pinFunction } : {}),
		        };
		    }
		}
		exports.NetConnectionInstance = NetConnectionInstance;
		
	} (nets));
	return nets;
}

var hasRequiredNetInfo;

function requireNetInfo () {
	if (hasRequiredNetInfo) return NetInfo;
	hasRequiredNetInfo = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetInfoInstance = exports.NetInfoSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const zod_partial_1 = requireZodPartial();
		const DifferentialPair_1 = requireDifferentialPair$1();
		const metadata_1 = requireMetadata();
		const nets_1 = requireNets();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		exports.NetInfoSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    metadata: metadata_1.MetadataSchema.optional(),
		    connections: nets_1.NetConnectionSchema.array(),
		    diffPair: DifferentialPair_1.DifferentialPairSchema.optional(),
		    isGlobal: zod_1.z.boolean().optional(),
		    isHierarchical: zod_1.z.boolean().optional(),
		});
		class NetInfoInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, rawOpts) {
		        super(context);
		        this.connections = [];
		        const { name, ...rest } = rawOpts;
		        const opts = (0, zod_partial_1.partialWithoutDefaults)(exports.NetInfoSchema).parse(rest);
		        this.name = this.netRefCtx.existingOrNewRef(name);
		        this.metadata = opts.metadata;
		        this.connections = opts.connections ?? [];
		        this.diffPair = opts.diffPair;
		        this.isGlobal = opts.isGlobal;
		        this.isHierarchical = opts.isHierarchical;
		    }
		    toJSON() {
		        return {
		            name: this.name.valueOf(),
		            metadata: this.metadata,
		            connections: this.connections,
		            diffPair: this.diffPair,
		            isGlobal: this.isGlobal,
		            isHierarchical: this.isHierarchical,
		        };
		    }
		    merge(other) {
		        this.name = other.name ? this.netRefCtx.existingOrNewRef(other.name) : this.name;
		        this.diffPair = other.diffPair ?? this.diffPair;
		        this.isGlobal = other.isGlobal ?? this.isGlobal;
		        this.isHierarchical = other.isHierarchical ?? this.isHierarchical;
		        this.metadata = {
		            ...(this.metadata ?? {}),
		            ...(other.metadata ?? {}),
		        };
		        this.connections = Array.from(new Set([...this.connections, ...(other.connections ?? [])]));
		        return this;
		    }
		}
		exports.NetInfoInstance = NetInfoInstance;
		
	} (NetInfo));
	return NetInfo;
}

var hasRequiredDesignFileNetInfo;

function requireDesignFileNetInfo () {
	if (hasRequiredDesignFileNetInfo) return DesignFileNetInfo;
	hasRequiredDesignFileNetInfo = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DesignFileNetInfoLabelInstance = exports.DesignFileNetInfoInstance = exports.DesignFileNetInfoSchema = exports.DesignFileNetInfoLabelSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const NetInfo_1 = requireNetInfo();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		exports.DesignFileNetInfoLabelSchema = zod_1.z.object({
		    type: zod_1.z.string().default('unknown'),
		    position: zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()]),
		    label: zod_1.z.string(),
		    isGlobal: zod_1.z.boolean().optional(),
		    isHierarchical: zod_1.z.boolean().optional(),
		    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
		});
		exports.DesignFileNetInfoSchema = zod_1.z.preprocess((data) => {
		    if (typeof data === 'object' && data !== null && 'localName' in data && !('localNames' in data)) {
		        return {
		            ...data,
		            localNames: [data.localName],
		        };
		    }
		    return data;
		}, NetInfo_1.NetInfoSchema.extend({
		    localName: zod_1.z.string().optional(),
		    localNames: zod_1.z.string().array().optional(),
		    netLabels: exports.DesignFileNetInfoLabelSchema.array().optional(),
		}));
		class DesignFileNetInfoInstance extends NetInfo_1.NetInfoInstance {
		    constructor(context, opts) {
		        super(context, opts);
		        this.netLabels = [];
		        if (opts.localName) {
		            this.localName = this.netRefCtx.existingOrNewRef(opts.localName);
		        }
		        if (opts.netLabels) {
		            this.netLabels = opts.netLabels.map((l) => new DesignFileNetInfoLabelInstance(context, l));
		        }
		    }
		    toJSON() {
		        return {
		            ...super.toJSON(),
		            localName: this.localName?.valueOf(),
		            netLabels: this.netLabels?.map((l) => l.toJSON()),
		        };
		    }
		    merge(other) {
		        super.merge(other);
		        return this;
		    }
		}
		exports.DesignFileNetInfoInstance = DesignFileNetInfoInstance;
		class DesignFileNetInfoLabelInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, opts) {
		        super(context);
		        this.position = opts.position;
		        this.label = opts.label;
		        this.isGlobal = opts.isGlobal;
		        this.isHierarchical = opts.isHierarchical;
		        this.metadata = opts.metadata;
		        this.type = opts.type;
		    }
		    toJSON() {
		        return {
		            type: this.type,
		            position: this.position,
		            label: this.label.valueOf(),
		            isGlobal: this.isGlobal,
		            isHierarchical: this.isHierarchical,
		            metadata: this.metadata,
		        };
		    }
		}
		exports.DesignFileNetInfoLabelInstance = DesignFileNetInfoLabelInstance;
		
	} (DesignFileNetInfo));
	return DesignFileNetInfo;
}

var hasRequiredModelBase;

function requireModelBase () {
	if (hasRequiredModelBase) return ModelBase;
	hasRequiredModelBase = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModelBaseInstance = exports.ModelBaseSchema = exports.ModelTypeSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const DesignFileNetInfo_1 = requireDesignFileNetInfo();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		exports.ModelTypeSchema = zod_1.default.enum(['schematic', 'pcb']);
		const ModelBaseSchema = (drawingTypeSchema) => zod_1.default.object({
		    modelType: exports.ModelTypeSchema,
		    designId: zod_1.default.string(),
		    designName: zod_1.default.string().optional(),
		    sourceFile: zod_1.default.string(),
		    drawings: zod_1.default.record(zod_1.default.coerce.number(), drawingTypeSchema.array()),
		    edges_bbox: zod_1.default.object({
		        minx: zod_1.default.number(),
		        miny: zod_1.default.number(),
		        maxx: zod_1.default.number(),
		        maxy: zod_1.default.number(),
		    }),
		    initial_zoombox: zod_1.default.object({
		        minx: zod_1.default.number(),
		        miny: zod_1.default.number(),
		        maxx: zod_1.default.number(),
		        maxy: zod_1.default.number(),
		    }),
		    grid: zod_1.default
		        .object({
		        distance: zod_1.default.number(),
		        units: zod_1.default.enum(['mm', 'microns', 'inch', 'mil']),
		        multiple: zod_1.default.number().optional(),
		        invertX: zod_1.default.boolean().optional(),
		        invertY: zod_1.default.boolean().optional(),
		    })
		        .optional(),
		    layerSet: zod_1.default.string(),
		    metadata: zod_1.default.record(zod_1.default.string(), zod_1.default.string()).optional(),
		    warnings: zod_1.default.string().array(),
		    nets: zod_1.default.record(zod_1.default.string(), DesignFileNetInfo_1.DesignFileNetInfoSchema),
		    font: zod_1.default
		        .object({
		        default: zod_1.default.string(),
		    })
		        .optional(),
		});
		exports.ModelBaseSchema = ModelBaseSchema;
		class ModelBaseInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, opts) {
		        super(context);
		        this.nets = new Map();
		        this.drawings = new Map();
		        this.edges_bbox = {
		            minx: Number.POSITIVE_INFINITY,
		            miny: Number.POSITIVE_INFINITY,
		            maxx: Number.NEGATIVE_INFINITY,
		            maxy: Number.NEGATIVE_INFINITY,
		        };
		        this.initial_zoombox = {
		            minx: Number.POSITIVE_INFINITY,
		            miny: Number.POSITIVE_INFINITY,
		            maxx: Number.NEGATIVE_INFINITY,
		            maxy: Number.NEGATIVE_INFINITY,
		        };
		        this.warnings = [];
		        this.modelType = opts.modelType;
		        this.designId = opts.designId;
		        this.designName = opts.designName;
		        this.sourceFile = opts.sourceFile;
		        this.drawings = new Map(Object.entries(opts.drawings ?? {}).map(([layer, drawings]) => [Number(layer), new Set(drawings)]));
		        this.edges_bbox = opts.edges_bbox ?? this.edges_bbox;
		        this.initial_zoombox = opts.initial_zoombox ?? this.initial_zoombox;
		        this.grid = opts.grid;
		        this.layerSet = opts.layerSet;
		        this.metadata = opts.metadata;
		        this.warnings = opts.warnings ?? [];
		        this.font = opts.font;
		        this.nets = new Map(Object.entries(opts.nets ?? {}).map(([name, net]) => [this.netRefCtx.existingOrNewRef(name), new DesignFileNetInfo_1.DesignFileNetInfoInstance(this.context, net)]));
		    }
		    serializeModelBaseInstanceProps() {
		        return {
		            modelType: this.modelType,
		            designId: this.designId,
		            designName: this.designName,
		            sourceFile: this.sourceFile,
		            edges_bbox: structuredClone(this.edges_bbox),
		            initial_zoombox: structuredClone(this.initial_zoombox),
		            grid: structuredClone(this.grid),
		            layerSet: structuredClone(this.layerSet),
		            metadata: structuredClone(this.metadata),
		            warnings: structuredClone(this.warnings),
		            font: structuredClone(this.font),
		            drawings: Object.fromEntries(Array.from(this.drawings.entries()).map(([layer, drawings]) => [layer, Array.from(drawings)])),
		            nets: Object.fromEntries(Array.from(this.nets.entries()).map(([name, net]) => [name, net.toJSON()])),
		        };
		    }
		    addNet(net) {
		        const netInstance = net instanceof DesignFileNetInfo_1.DesignFileNetInfoInstance ? net : new DesignFileNetInfo_1.DesignFileNetInfoInstance(this.context, net);
		        const netName = this.netRefCtx.existingOrNewRef(netInstance.name);
		        if (this.nets.has(netName)) {
		            this.nets.get(netName).merge(netInstance);
		        }
		        else {
		            this.nets.set(netInstance.name, netInstance);
		        }
		    }
		    getNet(name) {
		        return this.nets.get(this.netRefCtx.existingOrNewRef(name));
		    }
		    renameNet(oldName, newName) {
		        this.netRefCtx.existingOrNewRef(oldName).setName(newName);
		    }
		    addDrawing(layer, drawing) {
		        if (!this.drawings.has(layer)) {
		            this.drawings.set(layer, new Set());
		        }
		        this.drawings.get(layer).add(drawing);
		    }
		    getDrawingLayers() {
		        return Array.from(this.drawings.keys());
		    }
		    getDrawingsOnLayer(layer) {
		        return Array.from(this.drawings.get(layer) ?? new Set());
		    }
		}
		exports.ModelBaseInstance = ModelBaseInstance;
		
	} (ModelBase));
	return ModelBase;
}

var hasRequiredPcb;

function requirePcb () {
	if (hasRequiredPcb) return pcb;
	hasRequiredPcb = 1;
	Object.defineProperty(pcb, "__esModule", { value: true });
	pcb.PcbModelInstance = pcb.PcbModelSchema = void 0;
	const PcbDrawing_1 = requirePcbDrawing();
	const Module_1 = requireModule();
	const Segment_1 = requireSegment();
	const Circle_1 = requireCircle();
	const Zone_1 = requireZone();
	const Via_1 = requireVia();
	const Track_1 = requireTrack();
	const ModelBase_1 = requireModelBase();
	const zod_1 = /*@__PURE__*/ requireZod();
	const metadata_1 = requireMetadata();
	pcb.PcbModelSchema = (0, ModelBase_1.ModelBaseSchema)(PcbDrawing_1.PcbDrawingSchema).extend({
	    modelType: zod_1.default.literal('pcb'),
	    edges: zod_1.default.array(zod_1.default.union([Circle_1.CircleSchema, Segment_1.SegmentSchema])),
	    throughHoleLayers: zod_1.default.number().array(),
	    modules: Module_1.ModuleSchema.array(),
	    tracks: zod_1.default.record(zod_1.default.coerce.number(), Track_1.TrackSchema.array()),
	    zones: zod_1.default.record(zod_1.default.coerce.number(), Zone_1.ZoneSchema.array()),
	    vias: Via_1.ViaSchema.array(),
	    metadata: metadata_1.MetadataSchema,
	    warnings: zod_1.default.string().array(),
	});
	class PcbModelInstance extends ModelBase_1.ModelBaseInstance {
	    constructor(context, opts) {
	        super(context, {
	            modelType: 'pcb',
	            ...opts,
	            metadata: {
	                title: '',
	                revision: '',
	                company: '',
	                date: '',
	                ...(opts.metadata ?? {}),
	            },
	        });
	        this.edges = [];
	        this.throughHoleLayers = [];
	        this.modules = [];
	        this.tracks = {};
	        this.zones = {};
	        this.vias = [];
	        this.edges = opts.edges ?? [];
	        this.throughHoleLayers = opts.throughHoleLayers ?? [];
	        this.modules = opts.modules ?? [];
	        this.tracks = opts.tracks ?? {};
	        this.zones = opts.zones ?? {};
	        this.vias = opts.vias ?? [];
	    }
	    toJSON() {
	        return {
	            ...this.serializeModelBaseInstanceProps(),
	            metadata: structuredClone({
	                title: '',
	                revision: '',
	                company: '',
	                date: '',
	                ...this.metadata,
	            }),
	            edges: structuredClone(this.edges),
	            throughHoleLayers: structuredClone(this.throughHoleLayers),
	            modules: structuredClone(this.modules),
	            tracks: structuredClone(this.tracks),
	            zones: structuredClone(this.zones),
	            vias: structuredClone(this.vias),
	        };
	    }
	}
	pcb.PcbModelInstance = PcbModelInstance;
	
	return pcb;
}

var SchematicModel = {};

var SchematicModelPort = {};

var hasRequiredSchematicModelPort;

function requireSchematicModelPort () {
	if (hasRequiredSchematicModelPort) return SchematicModelPort;
	hasRequiredSchematicModelPort = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SchematicModelPortInstance = exports.SchematicModelPortNetEntryInstance = exports.SchematicModelPortSchema = exports.SchematicModelPortNetEntrySchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SignalDirection_1 = requireSignalDirection();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		exports.SchematicModelPortNetEntrySchema = zod_1.default.object({
		    name: zod_1.default.string(),
		    label: zod_1.default.string(),
		});
		exports.SchematicModelPortSchema = zod_1.default.object({
		    name: zod_1.default.string(),
		    nets: exports.SchematicModelPortNetEntrySchema.array(),
		    direction: SignalDirection_1.SignalDirectionSchema.optional(),
		});
		class SchematicModelPortNetEntryInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, opts) {
		        super(context);
		        this.name = context.netRefCtx.existingOrNewRef(opts.name);
		        this.label = opts.label;
		    }
		    toJSON() {
		        return {
		            name: this.name.valueOf(),
		            label: this.label,
		        };
		    }
		}
		exports.SchematicModelPortNetEntryInstance = SchematicModelPortNetEntryInstance;
		class SchematicModelPortInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, opts) {
		        super(context);
		        this.name = opts.name;
		        this.nets = opts.nets.map((n) => new SchematicModelPortNetEntryInstance(context, n));
		        this.direction = opts.direction;
		    }
		    toJSON() {
		        return {
		            name: this.name,
		            nets: this.nets.map((n) => n.toJSON()),
		            direction: this.direction,
		        };
		    }
		}
		exports.SchematicModelPortInstance = SchematicModelPortInstance;
		
	} (SchematicModelPort));
	return SchematicModelPort;
}

var hasRequiredSchematicModel;

function requireSchematicModel () {
	if (hasRequiredSchematicModel) return SchematicModel;
	hasRequiredSchematicModel = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SchematicModelInstance = exports.MultiSheetSchematicModelSchema = exports.SchematicModelSchema = exports.SchematicModelMultiInstanceConfigurationSchema = exports.MultiInstanceDisplayModuleSchema = exports.SchematicModelInstanceConfigurationSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const Module_1 = requireModule();
		const SchDrawing_1 = requireSchDrawing();
		const ModelBase_1 = requireModelBase();
		const SchematicModelPort_1 = requireSchematicModelPort();
		exports.SchematicModelInstanceConfigurationSchema = zod_1.z.object({
		    instanceSheetIndex: zod_1.z.number(),
		    instanceName: zod_1.z.string(),
		    instanceId: zod_1.z.string(),
		    sheetDisplayPath: zod_1.z.array(zod_1.z.string()).optional(),
		    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
		});
		exports.MultiInstanceDisplayModuleSchema = Module_1.ModuleSchema.extend({});
		exports.SchematicModelMultiInstanceConfigurationSchema = zod_1.z.object({
		    instances: exports.SchematicModelInstanceConfigurationSchema.array(),
		    multiInstanceModules: exports.MultiInstanceDisplayModuleSchema.array(),
		});
		exports.SchematicModelSchema = (0, ModelBase_1.ModelBaseSchema)(SchDrawing_1.SchDrawingSchema).extend({
		    modelType: zod_1.z.literal('schematic'),
		    sheetIndex: zod_1.z.number(),
		    modules: Module_1.ModuleSchema.array(),
		    ports: zod_1.z.record(zod_1.z.string(), SchematicModelPort_1.SchematicModelPortSchema).prefault({}),
		    multiInstanceConfiguration: exports.SchematicModelMultiInstanceConfigurationSchema.optional(),
		});
		exports.MultiSheetSchematicModelSchema = exports.SchematicModelSchema.extend({
		    multiInstanceConfiguration: exports.SchematicModelMultiInstanceConfigurationSchema,
		});
		class SchematicModelInstance extends ModelBase_1.ModelBaseInstance {
		    constructor(context, opts) {
		        super(context, {
		            modelType: 'schematic',
		            ...opts,
		            metadata: {
		                title: '',
		                revision: '',
		                company: '',
		                date: '',
		                ...(opts.metadata ?? {}),
		            },
		        });
		        this.modules = [];
		        this.ports = new Map();
		        this.sheetIndex = opts.sheetIndex ?? 0;
		        this.modules = Array.from(opts.modules ?? []).map((m) => new Module_1.ModuleInstance(context, m));
		        this.ports = new Map(Object.entries(opts.ports ?? {}).map(([name, port]) => [name, new SchematicModelPort_1.SchematicModelPortInstance(context, port)]));
		    }
		    addModule(module) {
		        this.modules.push(module instanceof Module_1.ModuleInstance ? module : new Module_1.ModuleInstance(this.context, module));
		    }
		    addPort(port) {
		        this.ports.set(port.name, port instanceof SchematicModelPort_1.SchematicModelPortInstance ? port : new SchematicModelPort_1.SchematicModelPortInstance(this.context, port));
		    }
		    toJSON() {
		        return {
		            ...this.serializeModelBaseInstanceProps(),
		            sheetIndex: this.sheetIndex,
		            modules: this.modules.map((m) => m.toJSON()),
		            ports: Object.fromEntries(Array.from(this.ports.entries()).map(([name, port]) => [name, port.toJSON()])),
		        };
		    }
		}
		exports.SchematicModelInstance = SchematicModelInstance;
		
	} (SchematicModel));
	return SchematicModel;
}

var hasRequiredDesign;

function requireDesign () {
	if (hasRequiredDesign) return Design;
	hasRequiredDesign = 1;
	Object.defineProperty(Design, "__esModule", { value: true });
	Design.DesignSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const pcb_1 = requirePcb();
	const SchematicModel_1 = requireSchematicModel();
	Design.DesignSchema = zod_1.default.discriminatedUnion('modelType', [
	    pcb_1.PcbModelSchema,
	    SchematicModel_1.SchematicModelSchema,
	]);
	
	return Design;
}

var DesignHierarchy = {};

var hasRequiredDesignHierarchy;

function requireDesignHierarchy () {
	if (hasRequiredDesignHierarchy) return DesignHierarchy;
	hasRequiredDesignHierarchy = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DesignHierarchyNodeSchema = void 0;
		exports.hierarchyFromDesigns = hierarchyFromDesigns;
		const zod_1 = /*@__PURE__*/ requireZod();
		function hierarchyFromDesigns(designs) {
		    return Object.values(designs).map((d) => ({
		        designId: d.designId,
		        label: d.designName ?? d.designId,
		        children: d.modelType === 'schematic' ?
		            d.multiInstanceConfiguration?.instances?.map((i) => ({
		                designId: d.designId,
		                label: i.instanceName,
		                instanceId: i.instanceId,
		            }))
		            : undefined,
		    }));
		}
		exports.DesignHierarchyNodeSchema = zod_1.z.lazy(() => zod_1.z.object({
		    designId: zod_1.z.string().optional(),
		    label: zod_1.z.string(),
		    instanceId: zod_1.z.string().optional(),
		    children: zod_1.z.array(exports.DesignHierarchyNodeSchema).optional(),
		}));
		
	} (DesignHierarchy));
	return DesignHierarchy;
}

var DrawingModels = {};

var FaceType = {};

var hasRequiredFaceType;

function requireFaceType () {
	if (hasRequiredFaceType) return FaceType;
	hasRequiredFaceType = 1;
	Object.defineProperty(FaceType, "__esModule", { value: true });
	FaceType.FaceTypeSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	FaceType.FaceTypeSchema = zod_1.z.enum(['F', 'B']);
	
	return FaceType;
}

var Layer = {};

var LayerType = {};

var hasRequiredLayerType;

function requireLayerType () {
	if (hasRequiredLayerType) return LayerType;
	hasRequiredLayerType = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.LayerTypeSchema = exports.SchLayerTypeSchema = exports.SchLayerType = exports.PcbLayerTypeSchema = exports.PcbLayerType = exports.UnknownLayerTypeSchema = exports.UnknownLayerType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		var UnknownLayerType;
		(function (UnknownLayerType) {
		    UnknownLayerType["Other"] = "Other";
		})(UnknownLayerType || (exports.UnknownLayerType = UnknownLayerType = {}));
		exports.UnknownLayerTypeSchema = zod_1.z.nativeEnum(UnknownLayerType);
		var PcbLayerType;
		(function (PcbLayerType) {
		    PcbLayerType["F"] = "F";
		    PcbLayerType["B"] = "B";
		    PcbLayerType["Inner"] = "Inner";
		    PcbLayerType["SilkS"] = "SilkS";
		    PcbLayerType["Edge"] = "Edge";
		    PcbLayerType["Paste"] = "Paste";
		    PcbLayerType["Mask"] = "Mask";
		    PcbLayerType["Other"] = "Other";
		    PcbLayerType["Virtual"] = "Virtual";
		})(PcbLayerType || (exports.PcbLayerType = PcbLayerType = {}));
		exports.PcbLayerTypeSchema = zod_1.z.nativeEnum(PcbLayerType);
		var SchLayerType;
		(function (SchLayerType) {
		    SchLayerType["Label"] = "Label";
		    SchLayerType["NameLabel"] = "NameLabel";
		    SchLayerType["ValueLabel"] = "ValueLabel";
		    SchLayerType["Pin"] = "Pin";
		    SchLayerType["PinName"] = "PinName";
		    SchLayerType["PinNumber"] = "PinNumber";
		    SchLayerType["Symbol"] = "Symbol";
		    SchLayerType["Wire"] = "Wire";
		    SchLayerType["Bus"] = "Bus";
		    SchLayerType["Junction"] = "Junction";
		    SchLayerType["BusJunction"] = "BusJunction";
		    SchLayerType["Other"] = "Other";
		    SchLayerType["Background"] = "Background";
		    SchLayerType["Image"] = "Image";
		})(SchLayerType || (exports.SchLayerType = SchLayerType = {}));
		exports.SchLayerTypeSchema = zod_1.z.nativeEnum(SchLayerType);
		exports.LayerTypeSchema = zod_1.z.union([exports.PcbLayerTypeSchema, exports.SchLayerTypeSchema, exports.UnknownLayerTypeSchema]);
		
	} (LayerType));
	return LayerType;
}

var hasRequiredLayer;

function requireLayer () {
	if (hasRequiredLayer) return Layer;
	hasRequiredLayer = 1;
	Object.defineProperty(Layer, "__esModule", { value: true });
	Layer.LayerSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const FaceType_1 = requireFaceType();
	const LayerType_1 = requireLayerType();
	Layer.LayerSchema = zod_1.z.object({
	    id: zod_1.z.number(),
	    name: zod_1.z.string(),
	    friendlyName: zod_1.z.string().optional(),
	    type: LayerType_1.LayerTypeSchema,
	    face: FaceType_1.FaceTypeSchema.optional(),
	    faceMirrorLayer: zod_1.z.number().optional(),
	    defaultVisible: zod_1.z.boolean().optional(),
	});
	
	return Layer;
}

var LayerSet = {};

var hasRequiredLayerSet;

function requireLayerSet () {
	if (hasRequiredLayerSet) return LayerSet;
	hasRequiredLayerSet = 1;
	Object.defineProperty(LayerSet, "__esModule", { value: true });
	LayerSet.LayerSetSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ModelBase_1 = requireModelBase();
	const Layer_1 = requireLayer();
	LayerSet.LayerSetSchema = zod_1.z.object({
	    type: zod_1.z.literal('layerSet'),
	    id: zod_1.z.string(),
	    layerSetType: ModelBase_1.ModelTypeSchema,
	    layers: Layer_1.LayerSchema.array(),
	});
	
	return LayerSet;
}

var PcbDrawingWithLayers = {};

var hasRequiredPcbDrawingWithLayers;

function requirePcbDrawingWithLayers () {
	if (hasRequiredPcbDrawingWithLayers) return PcbDrawingWithLayers;
	hasRequiredPcbDrawingWithLayers = 1;
	Object.defineProperty(PcbDrawingWithLayers, "__esModule", { value: true });
	PcbDrawingWithLayers.PcbDrawingWithLayersSchema = void 0;
	const DrawingWithLayers_1 = requireDrawingWithLayers();
	const PcbDrawing_1 = requirePcbDrawing();
	PcbDrawingWithLayers.PcbDrawingWithLayersSchema = (0, DrawingWithLayers_1.DrawingWithLayersSchemaT)(PcbDrawing_1.PcbDrawingSchema);
	
	return PcbDrawingWithLayers;
}

var hasRequiredDrawingModels;

function requireDrawingModels () {
	if (hasRequiredDrawingModels) return DrawingModels;
	hasRequiredDrawingModels = 1;
	(function (exports) {
		var __createBinding = (DrawingModels && DrawingModels.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (DrawingModels && DrawingModels.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAnyDrawingSchema(), exports);
		__exportStar(requireArc(), exports);
		__exportStar(requireBus(), exports);
		__exportStar(requireCircle(), exports);
		__exportStar(requireColorOverride(), exports);
		__exportStar(requireCurve(), exports);
		__exportStar(requireDifferentialPair$1(), exports);
		__exportStar(requireDrawingBase(), exports);
		__exportStar(requireDrawingGroup(), exports);
		__exportStar(requireDrawingWithLayers(), exports);
		__exportStar(requireFaceType(), exports);
		__exportStar(requireFootprint(), exports);
		__exportStar(requireImage(), exports);
		__exportStar(requireJunction(), exports);
		__exportStar(requireLabel$1(), exports);
		__exportStar(requireLayer(), exports);
		__exportStar(requireLayerSet(), exports);
		__exportStar(requireLayerType(), exports);
		__exportStar(requireLineStyle(), exports);
		__exportStar(requireModule(), exports);
		__exportStar(requireModuleDrawProperty(), exports);
		__exportStar(requireModuleTransform(), exports);
		__exportStar(requirePad(), exports);
		__exportStar(requirePcbDrawing(), exports);
		__exportStar(requirePcbDrawingWithLayers(), exports);
		__exportStar(requirePcbFootprint(), exports);
		__exportStar(requirePin(), exports);
		__exportStar(requirePolygon$1(), exports);
		__exportStar(requireRect(), exports);
		__exportStar(requireRichText(), exports);
		__exportStar(requireSchDrawing(), exports);
		__exportStar(requireSchDrawingWithLayers(), exports);
		__exportStar(requireSchFootprint(), exports);
		__exportStar(requireSchGroupDrawableTypes(), exports);
		__exportStar(requireSegment(), exports);
		__exportStar(requireText(), exports);
		__exportStar(requireTextAttr(), exports);
		__exportStar(requireTextBox(), exports);
		__exportStar(requireTextTransform(), exports);
		__exportStar(requireTrack(), exports);
		__exportStar(requireVia(), exports);
		__exportStar(requireXYPosition(), exports);
		__exportStar(requireZone(), exports);
		
	} (DrawingModels));
	return DrawingModels;
}

var Fonts = {};

var hasRequiredFonts$1;

function requireFonts$1 () {
	if (hasRequiredFonts$1) return Fonts;
	hasRequiredFonts$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IFontSchema = exports.IFontRenderSettingsSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.IFontRenderSettingsSchema = zod_1.default.object({
		    heightRatio: zod_1.default.number(),
		    fontRenderScale: zod_1.default.number(),
		    fontRenderOffset: zod_1.default.tuple([zod_1.default.number(), zod_1.default.number()]),
		    startChar: zod_1.default.number(),
		    unknownChar: zod_1.default.string(),
		    italicTilt: zod_1.default.number(),
		    charSpacing: zod_1.default.number(),
		    overbarSpacing: zod_1.default.number(),
		    underlineSpacing: zod_1.default.number(),
		    interlineRatio: zod_1.default.number(),
		});
		exports.IFontSchema = exports.IFontRenderSettingsSchema.extend({
		    glyphs: zod_1.default.string().array(),
		});
		
	} (Fonts));
	return Fonts;
}

var GlobalNetInfo = {};

var hasRequiredGlobalNetInfo;

function requireGlobalNetInfo () {
	if (hasRequiredGlobalNetInfo) return GlobalNetInfo;
	hasRequiredGlobalNetInfo = 1;
	Object.defineProperty(GlobalNetInfo, "__esModule", { value: true });
	GlobalNetInfo.GlobalNetInfoSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const metadata_1 = requireMetadata();
	GlobalNetInfo.GlobalNetInfoSchema = zod_1.z.object({
	    name: zod_1.z.string(),
	    metadata: metadata_1.MetadataSchema.optional(),
	});
	
	return GlobalNetInfo;
}

var libraryModel = {};

var hasRequiredLibraryModel;

function requireLibraryModel () {
	if (hasRequiredLibraryModel) return libraryModel;
	hasRequiredLibraryModel = 1;
	Object.defineProperty(libraryModel, "__esModule", { value: true });
	
	return libraryModel;
}

var modelWithComponents = {};

var hasRequiredModelWithComponents;

function requireModelWithComponents () {
	if (hasRequiredModelWithComponents) return modelWithComponents;
	hasRequiredModelWithComponents = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModelWithComponentsSchema = exports.ModelWithWarningsSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const component_1 = requireComponent();
		exports.ModelWithWarningsSchema = zod_1.z.object({
		    warnings: zod_1.z.string().array(),
		});
		exports.ModelWithComponentsSchema = exports.ModelWithWarningsSchema.extend({
		    components: zod_1.z.record(zod_1.z.string(), component_1.ComponentSchema),
		});
		
	} (modelWithComponents));
	return modelWithComponents;
}

var modelVersion = {};

var hasRequiredModelVersion;

function requireModelVersion () {
	if (hasRequiredModelVersion) return modelVersion;
	hasRequiredModelVersion = 1;
	Object.defineProperty(modelVersion, "__esModule", { value: true });
	modelVersion.isModelVersionAtLeast = isModelVersionAtLeast;
	function isModelVersionAtLeast(model, requiredVersion) {
	    const modelVersion = parseSemanticVersion(model.modelVersion);
	    const required = parseSemanticVersion(requiredVersion);
	    if (!modelVersion || !required) {
	        return false;
	    }
	    return (modelVersion.major > required.major ||
	        (modelVersion.major === required.major && modelVersion.minor > required.minor) ||
	        (modelVersion.major === required.major &&
	            modelVersion.minor === required.minor &&
	            modelVersion.patch >= required.patch));
	}
	function parseSemanticVersion(version) {
	    const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.exec(version ?? '');
	    if (!match) {
	        return undefined;
	    }
	    const [major, minor, patch] = match.slice(1).map(toSafeInteger);
	    if (major === undefined || minor === undefined || patch === undefined) {
	        return undefined;
	    }
	    return { major, minor, patch };
	}
	function toSafeInteger(digits) {
	    const value = Number(digits);
	    return Number.isSafeInteger(value) ? value : undefined;
	}
	
	return modelVersion;
}

var ModelWithNetStats = {};

var hasRequiredModelWithNetStats;

function requireModelWithNetStats () {
	if (hasRequiredModelWithNetStats) return ModelWithNetStats;
	hasRequiredModelWithNetStats = 1;
	Object.defineProperty(ModelWithNetStats, "__esModule", { value: true });
	
	return ModelWithNetStats;
}

var ModulePropertiesCacheFile = {};

var hasRequiredModulePropertiesCacheFile;

function requireModulePropertiesCacheFile () {
	if (hasRequiredModulePropertiesCacheFile) return ModulePropertiesCacheFile;
	hasRequiredModulePropertiesCacheFile = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModulePropertiesCacheFileSchema = exports.ModulePropertiesCacheEntrySchema = exports.ModulePropertiesCacheInstanceSchema = exports.MODULE_PROPERTIES_CACHE_SCHEMA_VERSION = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ModelBase_1 = requireModelBase();
		const metadata_1 = requireMetadata();
		const ModuleDrawProperty_1 = requireModuleDrawProperty();
		const CadTool_1 = requireCadTool();
		exports.MODULE_PROPERTIES_CACHE_SCHEMA_VERSION = 2;
		exports.ModulePropertiesCacheInstanceSchema = zod_1.z.object({
		    designId: zod_1.z.string(),
		    designModelType: ModelBase_1.ModelTypeSchema,
		    ref: zod_1.z.string(),
		    drawProperties: zod_1.z.record(zod_1.z.string(), ModuleDrawProperty_1.ModuleDrawPropertySchema),
		});
		exports.ModulePropertiesCacheEntrySchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    value: zod_1.z.string().optional(),
		    metadata: metadata_1.MetadataSchema.optional(),
		    instances: exports.ModulePropertiesCacheInstanceSchema.array(),
		});
		exports.ModulePropertiesCacheFileSchema = zod_1.z.object({
		    projectModelId: zod_1.z.string(),
		    schemaVersion: zod_1.z.literal(exports.MODULE_PROPERTIES_CACHE_SCHEMA_VERSION),
		    generatedAt: zod_1.z.string(),
		    cadTool: CadTool_1.CadToolSchema,
		    entries: exports.ModulePropertiesCacheEntrySchema.array(),
		});
		
	} (ModulePropertiesCacheFile));
	return ModulePropertiesCacheFile;
}

var ComponentsCacheFile = {};

var hasRequiredComponentsCacheFile;

function requireComponentsCacheFile () {
	if (hasRequiredComponentsCacheFile) return ComponentsCacheFile;
	hasRequiredComponentsCacheFile = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentsCacheFileSchema = exports.ComponentsCacheComponentSchema = exports.ComponentsCacheFootprintSchema = exports.ComponentsCachePadSchema = exports.ComponentsCacheSymbolSchema = exports.ComponentsCacheSymbolPinSchema = exports.ComponentsCacheSignalSchema = exports.COMPONENTS_CACHE_SCHEMA_VERSION = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const SignalId_1 = requireSignalId();
		exports.COMPONENTS_CACHE_SCHEMA_VERSION = 1;
		exports.ComponentsCacheSignalSchema = zod_1.z.object({
		    id: SignalId_1.SignalIdSchema,
		    name: zod_1.z.string(),
		    padRefs: zod_1.z.string().array(),
		    pinRefs: zod_1.z.string().nullable().array(),
		});
		exports.ComponentsCacheSymbolPinSchema = zod_1.z.object({
		    ref: zod_1.z.string(),
		    number: zod_1.z.string(),
		});
		exports.ComponentsCacheSymbolSchema = zod_1.z.object({
		    ref: zod_1.z.string(),
		    name: zod_1.z.string(),
		    pins: exports.ComponentsCacheSymbolPinSchema.array(),
		});
		exports.ComponentsCachePadSchema = zod_1.z.object({
		    ref: zod_1.z.string(),
		    name: zod_1.z.string(),
		});
		exports.ComponentsCacheFootprintSchema = zod_1.z.object({
		    ref: zod_1.z.string(),
		    pads: exports.ComponentsCachePadSchema.array(),
		});
		exports.ComponentsCacheComponentSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    signals: exports.ComponentsCacheSignalSchema.array(),
		    symbols: exports.ComponentsCacheSymbolSchema.array(),
		    footprints: exports.ComponentsCacheFootprintSchema.array(),
		});
		exports.ComponentsCacheFileSchema = zod_1.z.object({
		    projectModelId: zod_1.z.string(),
		    schemaVersion: zod_1.z.literal(exports.COMPONENTS_CACHE_SCHEMA_VERSION),
		    generatedAt: zod_1.z.string(),
		    components: zod_1.z.record(zod_1.z.string(), exports.ComponentsCacheComponentSchema),
		});
		
	} (ComponentsCacheFile));
	return ComponentsCacheFile;
}

var ModulesCacheFile = {};

var hasRequiredModulesCacheFile;

function requireModulesCacheFile () {
	if (hasRequiredModulesCacheFile) return ModulesCacheFile;
	hasRequiredModulesCacheFile = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModulesCacheFileSchema = exports.ModulesCacheSkippedInstanceSchema = exports.ModulesCacheSkipReason = exports.ModulesCacheEntrySchema = exports.MODULES_CACHE_SCHEMA_VERSION = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ModelBase_1 = requireModelBase();
		exports.MODULES_CACHE_SCHEMA_VERSION = 2;
		exports.ModulesCacheEntrySchema = zod_1.z.object({
		    key: zod_1.z.string(),
		    designId: zod_1.z.string(),
		    designModelType: ModelBase_1.ModelTypeSchema,
		    designName: zod_1.z.string().optional(),
		    ref: zod_1.z.string(),
		    name: zod_1.z.string(),
		    fullModuleName: zod_1.z.string().optional(),
		    componentRef: zod_1.z.string(),
		    footprintRef: zod_1.z.string(),
		    componentName: zod_1.z.string().optional(),
		    componentModelId: zod_1.z.string().optional(),
		    projectModuleName: zod_1.z.string(),
		    projectModuleValue: zod_1.z.string().optional(),
		    projectModuleIsDnp: zod_1.z.boolean().optional(),
		    catalogRef: zod_1.z.string().optional(),
		    multiInstanceId: zod_1.z.string().optional(),
		    pinNets: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    pinCount: zod_1.z.number().int().nonnegative(),
		});
		var ModulesCacheSkipReason;
		(function (ModulesCacheSkipReason) {
		    ModulesCacheSkipReason["DesignMissing"] = "design_missing";
		    ModulesCacheSkipReason["DesignModuleMissing"] = "design_module_missing";
		})(ModulesCacheSkipReason || (exports.ModulesCacheSkipReason = ModulesCacheSkipReason = {}));
		exports.ModulesCacheSkippedInstanceSchema = zod_1.z.object({
		    projectModuleName: zod_1.z.string(),
		    componentRef: zod_1.z.string(),
		    designId: zod_1.z.string(),
		    ref: zod_1.z.string(),
		    reason: zod_1.z.nativeEnum(ModulesCacheSkipReason),
		    designModelType: ModelBase_1.ModelTypeSchema.optional(),
		});
		exports.ModulesCacheFileSchema = zod_1.z.object({
		    projectModelId: zod_1.z.string(),
		    schemaVersion: zod_1.z.literal(exports.MODULES_CACHE_SCHEMA_VERSION),
		    generatedAt: zod_1.z.string(),
		    entries: exports.ModulesCacheEntrySchema.array(),
		    skippedInstances: exports.ModulesCacheSkippedInstanceSchema.array(),
		});
		
	} (ModulesCacheFile));
	return ModulesCacheFile;
}

var NetsCacheFile = {};

var hasRequiredNetsCacheFile;

function requireNetsCacheFile () {
	if (hasRequiredNetsCacheFile) return NetsCacheFile;
	hasRequiredNetsCacheFile = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetsCacheFileSchema = exports.NetsCacheEntrySchema = exports.NetsCacheConnectionSchema = exports.NETS_CACHE_SCHEMA_VERSION = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.NETS_CACHE_SCHEMA_VERSION = 1;
		exports.NetsCacheConnectionSchema = zod_1.z.object({
		    designId: zod_1.z.string(),
		    moduleRef: zod_1.z.string().describe('The module instance id.'),
		    moduleName: zod_1.z.string().describe('The module reference designator (e.g. R1).'),
		    pinRef: zod_1.z.string(),
		});
		exports.NetsCacheEntrySchema = zod_1.z.object({
		    key: zod_1.z.string(),
		    name: zod_1.z.string(),
		    isGlobal: zod_1.z.boolean(),
		    isHierarchical: zod_1.z.boolean(),
		    designIds: zod_1.z.string().array(),
		    connectionCount: zod_1.z.number().int().nonnegative(),
		    connections: exports.NetsCacheConnectionSchema.array(),
		});
		exports.NetsCacheFileSchema = zod_1.z.object({
		    projectModelId: zod_1.z.string(),
		    schemaVersion: zod_1.z.literal(exports.NETS_CACHE_SCHEMA_VERSION),
		    generatedAt: zod_1.z.string(),
		    entries: exports.NetsCacheEntrySchema.array(),
		});
		
	} (NetsCacheFile));
	return NetsCacheFile;
}

var Parametrics = {};

var DifferentialPairPolarityParametric = {};

var hasRequiredDifferentialPairPolarityParametric;

function requireDifferentialPairPolarityParametric () {
	if (hasRequiredDifferentialPairPolarityParametric) return DifferentialPairPolarityParametric;
	hasRequiredDifferentialPairPolarityParametric = 1;
	Object.defineProperty(DifferentialPairPolarityParametric, "__esModule", { value: true });
	DifferentialPairPolarityParametric.DifferentialPairPolarityParametricSchema = DifferentialPairPolarityParametric.DifferentialPairPolarityParametric = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var DifferentialPairPolarityParametric$1;
	(function (DifferentialPairPolarityParametric) {
	    DifferentialPairPolarityParametric["Positive"] = "p";
	    DifferentialPairPolarityParametric["Negative"] = "n";
	})(DifferentialPairPolarityParametric$1 || (DifferentialPairPolarityParametric.DifferentialPairPolarityParametric = DifferentialPairPolarityParametric$1 = {}));
	DifferentialPairPolarityParametric.DifferentialPairPolarityParametricSchema = zod_1.z.nativeEnum(DifferentialPairPolarityParametric$1);
	
	return DifferentialPairPolarityParametric;
}

var FetDriveParametric = {};

var hasRequiredFetDriveParametric;

function requireFetDriveParametric () {
	if (hasRequiredFetDriveParametric) return FetDriveParametric;
	hasRequiredFetDriveParametric = 1;
	Object.defineProperty(FetDriveParametric, "__esModule", { value: true });
	FetDriveParametric.FetDriveParametricSchema = FetDriveParametric.FetDriveParametric = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var FetDriveParametric$1;
	(function (FetDriveParametric) {
	    FetDriveParametric["OpenDrain"] = "open-drain";
	    FetDriveParametric["OpenCollector"] = "open-collector";
	    FetDriveParametric["PushPull"] = "push-pull";
	})(FetDriveParametric$1 || (FetDriveParametric.FetDriveParametric = FetDriveParametric$1 = {}));
	FetDriveParametric.FetDriveParametricSchema = zod_1.z.nativeEnum(FetDriveParametric$1);
	
	return FetDriveParametric;
}

var PinClampDiodeParametric = {};

var hasRequiredPinClampDiodeParametric;

function requirePinClampDiodeParametric () {
	if (hasRequiredPinClampDiodeParametric) return PinClampDiodeParametric;
	hasRequiredPinClampDiodeParametric = 1;
	Object.defineProperty(PinClampDiodeParametric, "__esModule", { value: true });
	PinClampDiodeParametric.PinClampDiodeParametricSchema = PinClampDiodeParametric.PinClampDiodeParametric = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var PinClampDiodeParametric$1;
	(function (PinClampDiodeParametric) {
	    PinClampDiodeParametric["ClampUp"] = "clamp-up";
	    PinClampDiodeParametric["ClampDown"] = "clamp-down";
	})(PinClampDiodeParametric$1 || (PinClampDiodeParametric.PinClampDiodeParametric = PinClampDiodeParametric$1 = {}));
	PinClampDiodeParametric.PinClampDiodeParametricSchema = zod_1.z.nativeEnum(PinClampDiodeParametric$1);
	
	return PinClampDiodeParametric;
}

var PinPullupParametric = {};

var hasRequiredPinPullupParametric;

function requirePinPullupParametric () {
	if (hasRequiredPinPullupParametric) return PinPullupParametric;
	hasRequiredPinPullupParametric = 1;
	Object.defineProperty(PinPullupParametric, "__esModule", { value: true });
	PinPullupParametric.PinPullupParametricSchema = PinPullupParametric.PinPullupParametric = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var PinPullupParametric$1;
	(function (PinPullupParametric) {
	    PinPullupParametric["Pullup"] = "pullup";
	    PinPullupParametric["Pulldown"] = "pulldown";
	    PinPullupParametric["PullUpDown"] = "pullupdown";
	})(PinPullupParametric$1 || (PinPullupParametric.PinPullupParametric = PinPullupParametric$1 = {}));
	PinPullupParametric.PinPullupParametricSchema = zod_1.z.nativeEnum(PinPullupParametric$1);
	
	return PinPullupParametric;
}

var hasRequiredParametrics;

function requireParametrics () {
	if (hasRequiredParametrics) return Parametrics;
	hasRequiredParametrics = 1;
	(function (exports) {
		var __createBinding = (Parametrics && Parametrics.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Parametrics && Parametrics.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireDifferentialPairPolarityParametric(), exports);
		__exportStar(requireFetDriveParametric(), exports);
		__exportStar(requireParametrics$1(), exports);
		__exportStar(requirePinClampDiodeParametric(), exports);
		__exportStar(requirePinPullupParametric(), exports);
		
	} (Parametrics));
	return Parametrics;
}

var PowerSignalType = {};

var hasRequiredPowerSignalType;

function requirePowerSignalType () {
	if (hasRequiredPowerSignalType) return PowerSignalType;
	hasRequiredPowerSignalType = 1;
	Object.defineProperty(PowerSignalType, "__esModule", { value: true });
	PowerSignalType.PowerSignalType = void 0;
	var PowerSignalType$1;
	(function (PowerSignalType) {
	    PowerSignalType["PowerGND"] = "gnd";
	    PowerSignalType["PowerVCC"] = "vcc";
	    PowerSignalType["PowerVEE"] = "vee";
	    PowerSignalType["PowerAnalogGND"] = "agnd";
	    PowerSignalType["PowerAnalogVCC"] = "avcc";
	})(PowerSignalType$1 || (PowerSignalType.PowerSignalType = PowerSignalType$1 = {}));
	
	return PowerSignalType;
}

var ProjectDefinition = {};

var hasRequiredProjectDefinition;

function requireProjectDefinition () {
	if (hasRequiredProjectDefinition) return ProjectDefinition;
	hasRequiredProjectDefinition = 1;
	Object.defineProperty(ProjectDefinition, "__esModule", { value: true });
	
	return ProjectDefinition;
}

var ProjectFontConfiguration = {};

var hasRequiredProjectFontConfiguration;

function requireProjectFontConfiguration () {
	if (hasRequiredProjectFontConfiguration) return ProjectFontConfiguration;
	hasRequiredProjectFontConfiguration = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ProjectFontConfigurationSchema = exports.BrowserFontSchema = exports.ProjectFontSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const Fonts_1 = requireFonts$1();
		const TextAttr_1 = requireTextAttr();
		exports.ProjectFontSchema = Fonts_1.IFontRenderSettingsSchema.extend({
		    type: zod_1.default.literal('project'),
		    name: zod_1.default.string(),
		    glyphs: zod_1.default.record(zod_1.default.coerce.number(), zod_1.default.string().array()),
		});
		exports.BrowserFontSchema = zod_1.default.object({
		    type: zod_1.default.literal('browser'),
		    name: zod_1.default.string(),
		    attrs: TextAttr_1.TextAttrSchema.array().optional(),
		    thickness: zod_1.default.number().optional(),
		    size: zod_1.default.number().optional(),
		    rasterSizeCorrection: zod_1.default.number().optional(),
		});
		exports.ProjectFontConfigurationSchema = zod_1.default.object({
		    fonts: zod_1.default.record(zod_1.default.string(), zod_1.default.preprocess((val) => {
		        if (typeof val === 'object' && val !== null && !('type' in val)) {
		            return { ...val, type: 'project' };
		        }
		        return val;
		    }, zod_1.default.discriminatedUnion('type', [exports.ProjectFontSchema, exports.BrowserFontSchema]))),
		});
		
	} (ProjectFontConfiguration));
	return ProjectFontConfiguration;
}

var NetRef = {};

var hasRequiredNetRef;

function requireNetRef () {
	if (hasRequiredNetRef) return NetRef;
	hasRequiredNetRef = 1;
	Object.defineProperty(NetRef, "__esModule", { value: true });
	NetRef.NetRef = NetRef.NetRefCtx = void 0;
	NetRef.isNetRef = isNetRef;
	class NetRefCtx {
	    constructor() {
	        this.uniqueRefNo = 0;
	        this.map = new Map();
	        this.oldRefs = new Set();
	        this.handlers = {};
	    }
	    existingOrNewRef(value) {
	        if (value instanceof NetRef$1 || typeof value !== 'string') {
	            value = value.valueOf();
	        }
	        if (!this.map.has(value)) {
	            this.map.set(value, new NetRef$1(this, value));
	        }
	        return this.map.get(value);
	    }
	    setRef(oldRef, newRef) {
	        this.map.set(oldRef.valueOf(), newRef);
	        if (oldRef.valueOf() !== newRef.valueOf()) {
	            this.oldRefs.add(oldRef);
	            this.map.set(newRef.valueOf(), newRef);
	        }
	    }
	    clearOldRefs() {
	        for (const ref of this.oldRefs) {
	            this.map.delete(ref);
	        }
	        this.oldRefs.clear();
	    }
	    on(evt, handler) {
	        if (!this.handlers[evt]) {
	            this.handlers[evt] = [];
	        }
	        this.handlers[evt].push(handler);
	    }
	    off(evt, handler) {
	        if (!this.handlers[evt]) {
	            return;
	        }
	        this.handlers[evt] = this.handlers[evt].filter((h) => h !== handler);
	    }
	    getUniqueRefNo() {
	        return this.uniqueRefNo++;
	    }
	}
	NetRef.NetRefCtx = NetRefCtx;
	class NetRef$1 extends String {
	    constructor(ctx, value) {
	        super();
	        this.ctx = ctx;
	        this.handlers = {};
	        const self = this;
	        this.internalRef = value instanceof NetRef$1 ? value.internalRef.valueOf() : value;
	        this.ctx?.setRef(this.internalRef, this);
	        return new Proxy(this, {
	            get(target, prop) {
	                if (typeof prop === 'symbol') {
	                    return target[prop];
	                }
	                if (Number(prop) == prop && !(prop in target)) {
	                    return self.internalRef[prop];
	                }
	                return target[prop];
	            },
	        });
	    }
	    on(evt, handler) {
	        if (!this.handlers[evt]) {
	            this.handlers[evt] = [];
	        }
	        this.handlers[evt].push(handler);
	    }
	    off(evt, handler) {
	        if (!this.handlers[evt]) {
	            return;
	        }
	        this.handlers[evt] = this.handlers[evt].filter((h) => h !== handler);
	    }
	    setName(newVal) {
	        if (newVal instanceof NetRef$1) {
	            newVal = newVal.internalRef;
	        }
	        const oldVal = this.internalRef;
	        if (oldVal === newVal) {
	            return;
	        }
	        this.internalRef = newVal;
	        this.ctx?.setRef(oldVal, this);
	        if (this.handlers['change']) {
	            for (const handler of this.handlers['change']) {
	                handler(this);
	            }
	        }
	    }
	    toString() {
	        return this.internalRef.valueOf();
	    }
	    strCast() {
	        return this;
	    }
	    toJSON() {
	        return this.internalRef.valueOf();
	    }
	    valueOf() {
	        return this.internalRef.valueOf();
	    }
	    [Symbol.toPrimitive](_hint) {
	        return this.internalRef.valueOf();
	    }
	    get [Symbol.toStringTag]() {
	        return `NetRef (${this.internalRef.valueOf()})`;
	    }
	    static fromString(ctx, value) {
	        if (value instanceof NetRef$1) {
	            return value;
	        }
	        if (!ctx) {
	            return new NetRef$1(undefined, value);
	        }
	        return ctx.existingOrNewRef(value);
	    }
	    at(index) {
	        return this.internalRef[index];
	    }
	    get length() {
	        return this.internalRef.length;
	    }
	    charAt(index) {
	        return this.internalRef.charAt(index);
	    }
	    charCodeAt(index) {
	        return this.internalRef.charCodeAt(index);
	    }
	    concat(...strings) {
	        return this.internalRef.concat(...strings);
	    }
	    includes(searchString, position) {
	        return this.internalRef.includes(searchString, position);
	    }
	    endsWith(searchString, length) {
	        return this.internalRef.endsWith(searchString, length);
	    }
	    indexOf(searchString, position) {
	        return this.internalRef.indexOf(searchString, position);
	    }
	    lastIndexOf(searchString, position) {
	        return this.internalRef.lastIndexOf(searchString, position);
	    }
	    localeCompare(that, locales, options) {
	        return this.internalRef.localeCompare(that, locales, options);
	    }
	    match(regexp) {
	        return this.internalRef.match(regexp);
	    }
	    matchAll(regexp) {
	        return this.internalRef.matchAll(regexp);
	    }
	    normalize(form) {
	        return this.internalRef.normalize(form);
	    }
	    padEnd(targetLength, padString) {
	        return this.internalRef.padEnd(targetLength, padString);
	    }
	    padStart(targetLength, padString) {
	        return this.internalRef.padStart(targetLength, padString);
	    }
	    repeat(count) {
	        return this.internalRef.repeat(count);
	    }
	    replace(...args) {
	        return this.internalRef.replace(...args);
	    }
	    search(regexp) {
	        return this.internalRef.search(regexp);
	    }
	    slice(start, end) {
	        return this.internalRef.slice(start, end);
	    }
	    split(separator, limit) {
	        return this.internalRef.split(separator, limit);
	    }
	    startsWith(searchString, position) {
	        return this.internalRef.startsWith(searchString, position);
	    }
	    substring(start, end) {
	        return this.internalRef.substring(start, end);
	    }
	    toLocaleLowerCase(locales) {
	        return this.internalRef.toLocaleLowerCase(locales);
	    }
	    toLocaleUpperCase(locales) {
	        return this.internalRef.toLocaleUpperCase(locales);
	    }
	    toLowerCase() {
	        return this.internalRef.toLowerCase();
	    }
	    toUpperCase() {
	        return this.internalRef.toUpperCase();
	    }
	    trim() {
	        return this.internalRef.trim();
	    }
	    trimEnd() {
	        return this.internalRef.trimEnd();
	    }
	    trimStart() {
	        return this.internalRef.trimStart();
	    }
	    [Symbol.iterator]() {
	        return this.internalRef[Symbol.iterator]();
	    }
	}
	NetRef.NetRef = NetRef$1;
	function isNetRef(value) {
	    return typeof value === 'object' && value instanceof NetRef$1;
	}
	
	return NetRef;
}

var ProjectInstanceContext = {};

var hasRequiredProjectInstanceContext;

function requireProjectInstanceContext () {
	if (hasRequiredProjectInstanceContext) return ProjectInstanceContext;
	hasRequiredProjectInstanceContext = 1;
	Object.defineProperty(ProjectInstanceContext, "__esModule", { value: true });
	ProjectInstanceContext.ProjectInstanceContext = void 0;
	const NetRef_1 = requireNetRef();
	class ProjectInstanceContext$1 {
	    constructor(netRefCtx = new NetRef_1.NetRefCtx()) {
	        this.netRefCtx = netRefCtx;
	    }
	}
	ProjectInstanceContext.ProjectInstanceContext = ProjectInstanceContext$1;
	
	return ProjectInstanceContext;
}

var ProjectModel$1 = {};

var ProjectModule = {};

var hasRequiredProjectModule;

function requireProjectModule () {
	if (hasRequiredProjectModule) return ProjectModule;
	hasRequiredProjectModule = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ProjectModuleSchema = exports.ProjectModuleInstanceSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const StandardComponentModel_1 = requireStandardComponentModel();
		const metadata_1 = requireMetadata();
		exports.ProjectModuleInstanceSchema = zod_1.default.object({
		    ref: zod_1.default.string(),
		    designId: zod_1.default.string(),
		});
		exports.ProjectModuleSchema = zod_1.default.object({
		    name: zod_1.default.string(),
		    instances: exports.ProjectModuleInstanceSchema.array(),
		    metadata: metadata_1.MetadataSchema.optional(),
		    value: zod_1.default.string().optional(),
		    isDnp: zod_1.default.boolean().optional(),
		    hideInBom: zod_1.default.boolean().optional(),
		    model: StandardComponentModel_1.StandardComponentModelSchema.optional(),
		    componentRef: zod_1.default.string(),
		    catalogRef: zod_1.default.string().optional(),
		});
		
	} (ProjectModule));
	return ProjectModule;
}

var hasRequiredProjectModel$1;

function requireProjectModel$1 () {
	if (hasRequiredProjectModel$1) return ProjectModel$1;
	hasRequiredProjectModel$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ProjectModelInstance = exports.ProjectModelSchema = exports.ProjectModelAssetSchema = exports.AssetMimeTypeSchema = exports.ProjectModelAssetTypeSchema = exports.ProjectModelAssetType = void 0;
		const Layer_1 = requireLayer();
		const component_1 = requireComponent();
		const pcb_1 = requirePcb();
		const SchematicModel_1 = requireSchematicModel();
		const MessageResult_1 = requireMessageResult();
		const ProjectFontConfiguration_1 = requireProjectFontConfiguration();
		const ProjectModule_1 = requireProjectModule();
		const CadTool_1 = requireCadTool();
		const ProjectDataTypeInstance_1 = requireProjectDataTypeInstance();
		const metadata_1 = requireMetadata();
		const zod_1 = /*@__PURE__*/ requireZod();
		const GlobalNetInfo_1 = requireGlobalNetInfo();
		const DesignHierarchy_1 = requireDesignHierarchy();
		const Image_1 = requireImage();
		var ProjectModelAssetType;
		(function (ProjectModelAssetType) {
		    ProjectModelAssetType["Image"] = "image";
		    ProjectModelAssetType["Design"] = "design";
		    ProjectModelAssetType["ComponentDefinition"] = "cpdef";
		    ProjectModelAssetType["ProcessingResults"] = "procres";
		})(ProjectModelAssetType || (exports.ProjectModelAssetType = ProjectModelAssetType = {}));
		exports.ProjectModelAssetTypeSchema = zod_1.default.nativeEnum(ProjectModelAssetType);
		exports.AssetMimeTypeSchema = zod_1.default.union([Image_1.ImageMimeTypeSchema, zod_1.default.literal('application/json')]);
		exports.ProjectModelAssetSchema = zod_1.default.object({
		    type: exports.ProjectModelAssetTypeSchema,
		    ref: zod_1.default.string(),
		    name: zod_1.default.string(),
		    mimeType: exports.AssetMimeTypeSchema,
		});
		exports.ProjectModelSchema = zod_1.default.object({
		    modelVersion: zod_1.default.string().optional(),
		    type: CadTool_1.CadToolSchema,
		    name: zod_1.default.string().optional(),
		    layerSets: zod_1.default.record(zod_1.default.string(), zod_1.default.array(Layer_1.LayerSchema)),
		    designs: zod_1.default.record(zod_1.default.string(), zod_1.default.discriminatedUnion('modelType', [pcb_1.PcbModelSchema, SchematicModel_1.SchematicModelSchema])),
		    components: zod_1.default.record(zod_1.default.string(), component_1.ComponentSchema),
		    modules: ProjectModule_1.ProjectModuleSchema.array(),
		    warnings: zod_1.default.string().array(),
		    processingResults: (0, MessageResult_1.AggregateMessageResultSchema)(zod_1.default.literal('project-processing'), MessageResult_1.MessageResultSchemaDefault).prefault({
		        type: 'project-processing',
		        message: '',
		        innerResults: [],
		    }),
		    fonts: ProjectFontConfiguration_1.ProjectFontConfigurationSchema.optional(),
		    globalNets: zod_1.default.record(zod_1.default.string(), GlobalNetInfo_1.GlobalNetInfoSchema),
		    metadata: metadata_1.MetadataSchema.optional(),
		    designHierarchy: DesignHierarchy_1.DesignHierarchyNodeSchema.array().optional(),
		    assets: exports.ProjectModelAssetSchema.array().optional(),
		});
		class ProjectModelInstance extends ProjectDataTypeInstance_1.ProjectDataTypeInstance {
		    constructor(context, opts) {
		        super(context);
		        this.layerSets = {};
		        this.designs = {};
		        this.components = {};
		        this.modules = [];
		        this.warnings = [];
		        this.processingResults = {
		            type: 'project-processing',
		            message: '',
		            innerResults: [],
		        };
		    }
		    getGlobalNetsObject() {
		        return Object.fromEntries(this.globalNets.map((n) => [n.name, n.toJSON()]));
		    }
		    toJSON() {
		        return {
		            type: this.type,
		            name: this.name,
		            layerSets: this.layerSets,
		            designs: this.designs,
		            components: this.components,
		            modules: this.modules,
		            warnings: this.warnings,
		            processingResults: this.processingResults,
		            fonts: this.fonts,
		            globalNets: this.getGlobalNetsObject(),
		        };
		    }
		}
		exports.ProjectModelInstance = ProjectModelInstance;
		
	} (ProjectModel$1));
	return ProjectModel$1;
}

var ProjectModelManifest = {};

var hasRequiredProjectModelManifest;

function requireProjectModelManifest () {
	if (hasRequiredProjectModelManifest) return ProjectModelManifest;
	hasRequiredProjectModelManifest = 1;
	Object.defineProperty(ProjectModelManifest, "__esModule", { value: true });
	
	return ProjectModelManifest;
}

var RelativeDefinition = {};

var hasRequiredRelativeDefinition;

function requireRelativeDefinition () {
	if (hasRequiredRelativeDefinition) return RelativeDefinition;
	hasRequiredRelativeDefinition = 1;
	Object.defineProperty(RelativeDefinition, "__esModule", { value: true });
	
	return RelativeDefinition;
}

var ruleFailure = {};

var hasRequiredRuleFailure;

function requireRuleFailure () {
	if (hasRequiredRuleFailure) return ruleFailure;
	hasRequiredRuleFailure = 1;
	Object.defineProperty(ruleFailure, "__esModule", { value: true });
	
	return ruleFailure;
}

var SignalInstance = {};

var hasRequiredSignalInstance;

function requireSignalInstance () {
	if (hasRequiredSignalInstance) return SignalInstance;
	hasRequiredSignalInstance = 1;
	Object.defineProperty(SignalInstance, "__esModule", { value: true });
	
	return SignalInstance;
}

var SignalStructuralBase = {};

var hasRequiredSignalStructuralBase;

function requireSignalStructuralBase () {
	if (hasRequiredSignalStructuralBase) return SignalStructuralBase;
	hasRequiredSignalStructuralBase = 1;
	Object.defineProperty(SignalStructuralBase, "__esModule", { value: true });
	SignalStructuralBase.SignalStructuralBaseSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SignalId_1 = requireSignalId();
	SignalStructuralBase.SignalStructuralBaseSchema = zod_1.z.object({
	    id: SignalId_1.SignalIdSchema,
	    name: zod_1.z.string(),
	});
	
	return SignalStructuralBase;
}

var Utils$3 = {};

var ModeFiltering = {};

var hasRequiredModeFiltering;

function requireModeFiltering () {
	if (hasRequiredModeFiltering) return ModeFiltering;
	hasRequiredModeFiltering = 1;
	Object.defineProperty(ModeFiltering, "__esModule", { value: true });
	ModeFiltering.filterModesToDriveConditionsOnly = filterModesToDriveConditionsOnly;
	ModeFiltering.filterModesToReferencedOnly = filterModesToReferencedOnly;
	const ModeReferenceCondition_1 = requireModeReferenceCondition();
	function filterModesToDriveConditionsOnly(modes, signals) {
	    const referencedModeIds = new Set();
	    for (const signal of signals) {
	        if (!signal.supportedDriveModes || signal.supportedDriveModes.length === 0) {
	            continue;
	        }
	        for (const driveMode of signal.supportedDriveModes) {
	            if (!driveMode.conditions || driveMode.conditions.length === 0) {
	                continue;
	            }
	            for (const condition of driveMode.conditions) {
	                if ('condition' in condition && 'mode' in condition.condition) {
	                    referencedModeIds.add(condition.condition.mode);
	                }
	            }
	        }
	    }
	    return modes.filter((mode) => referencedModeIds.has(mode.id));
	}
	function collectDirectModeReferences(signals, boundaries) {
	    const referenced = new Set();
	    for (const signal of signals) {
	        for (const driveMode of signal.supportedDriveModes ?? []) {
	            for (const condition of driveMode.conditions ?? []) {
	                if ('condition' in condition && 'mode' in condition.condition) {
	                    referenced.add(condition.condition.mode);
	                }
	            }
	        }
	    }
	    for (const boundary of boundaries) {
	        for (const condition of boundary.conditions ?? []) {
	            if ((0, ModeReferenceCondition_1.isModeReferenceCondition)(condition)) {
	                referenced.add(condition.mode);
	            }
	        }
	    }
	    return referenced;
	}
	function filterModesToReferencedOnly(modes, signals, boundaries) {
	    if (modes.length === 0) {
	        return [];
	    }
	    const referencedModeIds = collectDirectModeReferences(signals, boundaries);
	    const modesById = new Map(modes.map((mode) => [mode.id, mode]));
	    const queue = [...referencedModeIds];
	    while (queue.length > 0) {
	        const id = queue.pop();
	        if (id === undefined) {
	            continue;
	        }
	        const mode = modesById.get(id);
	        if (mode === undefined) {
	            continue;
	        }
	        for (const precondition of mode.preconditions ?? []) {
	            if ((0, ModeReferenceCondition_1.isModeReferenceCondition)(precondition.condition) && !referencedModeIds.has(precondition.condition.mode)) {
	                referencedModeIds.add(precondition.condition.mode);
	                queue.push(precondition.condition.mode);
	            }
	        }
	    }
	    return modes.filter((mode) => referencedModeIds.has(mode.id));
	}
	
	return ModeFiltering;
}

var DrawingOnlyNetProcessor = {};

var hasRequiredDrawingOnlyNetProcessor;

function requireDrawingOnlyNetProcessor () {
	if (hasRequiredDrawingOnlyNetProcessor) return DrawingOnlyNetProcessor;
	hasRequiredDrawingOnlyNetProcessor = 1;
	Object.defineProperty(DrawingOnlyNetProcessor, "__esModule", { value: true });
	DrawingOnlyNetProcessor.DrawingOnlyNetProcessor = void 0;
	class DrawingOnlyNetProcessor$1 {
	    static removeDrawingOnlyNets(project) {
	        const namesReachingRealComponents = DrawingOnlyNetProcessor$1.collectNamesReachingRealComponents(project);
	        const removed = [];
	        for (const [designId, design] of Object.entries(project.designs)) {
	            for (const [netName, netInfo] of Object.entries(design.nets)) {
	                if (namesReachingRealComponents.has(netName)) {
	                    continue;
	                }
	                if (!DrawingOnlyNetProcessor$1.isDrawingOnlyNet(project, design, netInfo)) {
	                    continue;
	                }
	                removed.push({
	                    designId,
	                    netName,
	                    moduleNames: netInfo.connections.map((c) => c.moduleName),
	                });
	                delete design.nets[netName];
	                DrawingOnlyNetProcessor$1.clearPinNetsForNet(design, netName);
	            }
	        }
	        return removed;
	    }
	    static isDrawingOnlyNet(project, design, netInfo) {
	        if (netInfo.connections.length === 0) {
	            return false;
	        }
	        return netInfo.connections.every((connection) => {
	            const module = design.modules.find((m) => m.ref === connection.moduleRef);
	            if (!module) {
	                return true;
	            }
	            return project.components[module.componentRef]?.isVirtual === true;
	        });
	    }
	    static collectNamesReachingRealComponents(project) {
	        const names = new Set(Object.keys(project.globalNets ?? {}));
	        for (const design of Object.values(project.designs)) {
	            for (const [netName, netInfo] of Object.entries(design.nets)) {
	                if (names.has(netName)) {
	                    continue;
	                }
	                const reachesReal = netInfo.connections.some((connection) => {
	                    const module = design.modules.find((m) => m.ref === connection.moduleRef);
	                    return module !== undefined && project.components[module.componentRef]?.isVirtual !== true;
	                });
	                if (reachesReal) {
	                    names.add(netName);
	                }
	            }
	        }
	        return names;
	    }
	    static clearPinNetsForNet(design, netName) {
	        for (const module of design.modules) {
	            for (const [pinRef, assigned] of Object.entries(module.pinNets)) {
	                if (assigned === netName) {
	                    delete module.pinNets[pinRef];
	                }
	            }
	        }
	    }
	}
	DrawingOnlyNetProcessor.DrawingOnlyNetProcessor = DrawingOnlyNetProcessor$1;
	
	return DrawingOnlyNetProcessor;
}

var VirtualNetProcessor = {};

var hasRequiredVirtualNetProcessor;

function requireVirtualNetProcessor () {
	if (hasRequiredVirtualNetProcessor) return VirtualNetProcessor;
	hasRequiredVirtualNetProcessor = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.VirtualNetProcessor = exports.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE = exports.VIRTUAL_NET_METADATA_KEY = void 0;
		const BoundaryLevel_1 = requireBoundaryLevel();
		exports.VIRTUAL_NET_METADATA_KEY = 'virtual';
		exports.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE = 'noconnect';
		class VirtualNetProcessor {
		    static assignVirtualNetsToNoConnectPins(project) {
		        for (const design of Object.values(project.designs)) {
		            VirtualNetProcessor.assignVirtualNetsForDesign(project, design);
		        }
		    }
		    static assignVirtualNetsForDesign(project, design) {
		        const existingNetNames = new Set([...Object.keys(design.nets), ...Object.keys(project.globalNets ?? {})]);
		        for (const module of design.modules) {
		            VirtualNetProcessor.assignVirtualNetsForModule(project, design, module, existingNetNames);
		        }
		    }
		    static assignVirtualNetsForModule(project, design, module, existingNetNames) {
		        const component = project.components[module.componentRef];
		        if (!component) {
		            project.processingResults.innerResults.push({
		                type: 'virtual-net-processor-missing-module',
		                message: `VirtualNetProcessor: module "${module.name}" (${module.ref}) references unknown component "${module.componentRef}"; skipping no-connect pin processing`,
		                severity: BoundaryLevel_1.BoundaryLevel.Error,
		            });
		            return;
		        }
		        if (component.isVirtual) {
		            return;
		        }
		        const pins = VirtualNetProcessor.getPinsForModule(component, module, design);
		        if (pins.length === 0) {
		            return;
		        }
		        const processedPinRefs = new Set();
		        for (const pin of pins) {
		            if (processedPinRefs.has(pin.ref)) {
		                continue;
		            }
		            processedPinRefs.add(pin.ref);
		            const existing = module.pinNets[pin.ref];
		            if (typeof existing === 'string' && existing.length > 0) {
		                continue;
		            }
		            const signal = VirtualNetProcessor.findSignalForPin(component, pin.ref);
		            const virtualNetName = VirtualNetProcessor.makeUniqueVirtualNetName(module, pin, signal, existingNetNames);
		            module.pinNets[pin.ref] = virtualNetName;
		            const netInfo = {
		                name: virtualNetName,
		                connections: [
		                    {
		                        moduleRef: module.ref,
		                        moduleName: module.name,
		                        pinRef: pin.ref,
		                    },
		                ],
		                metadata: { [exports.VIRTUAL_NET_METADATA_KEY]: exports.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE },
		                netLabels: [],
		            };
		            design.nets[virtualNetName] = netInfo;
		        }
		    }
		    static getPinsForModule(component, module, design) {
		        if (design.modelType === 'schematic') {
		            const symbol = component.symbols.find((s) => s.ref === module.footprintRef);
		            if (!symbol) {
		                return [];
		            }
		            return symbol.pins.map((p) => ({ ref: p.ref, name: p.name, number: p.number }));
		        }
		        const footprint = component.footprints.find((f) => f.ref === module.footprintRef);
		        if (!footprint) {
		            return [];
		        }
		        return footprint.pads.map((p) => ({ ref: p.ref, name: p.name }));
		    }
		    static findSignalForPin(component, pinRef) {
		        return component.signals.find((s) => s.pinRefs.some((ref) => ref === pinRef));
		    }
		    static makeUniqueVirtualNetName(module, pin, signal, existingNetNames) {
		        const identifier = pin.number && pin.number.length > 0 ? pin.number : pin.ref;
		        const descriptor = pin.name.length > 0 ? pin.name : (signal?.name ?? '');
		        const rawSegments = [module.name, identifier, descriptor].filter((s) => s.length > 0);
		        const base = VirtualNetProcessor.sanitizeNetName(rawSegments.join('_'));
		        const safeBase = base.length > 0 ? base : VirtualNetProcessor.sanitizeNetName(`${module.name}_${pin.ref}`);
		        const finalBase = safeBase.length > 0 ? safeBase : 'virtual_net';
		        let candidate = finalBase;
		        let suffix = 1;
		        while (existingNetNames.has(candidate)) {
		            candidate = `${finalBase}_${suffix}`;
		            suffix++;
		        }
		        existingNetNames.add(candidate);
		        return candidate;
		    }
		    static sanitizeNetName(name) {
		        return name
		            .replace(/[^a-zA-Z0-9_\-.()]/g, '_')
		            .replace(/_+/g, '_')
		            .replace(/^_+|_+$/g, '');
		    }
		}
		exports.VirtualNetProcessor = VirtualNetProcessor;
		
	} (VirtualNetProcessor));
	return VirtualNetProcessor;
}

var hasRequiredUtils$3;

function requireUtils$3 () {
	if (hasRequiredUtils$3) return Utils$3;
	hasRequiredUtils$3 = 1;
	(function (exports) {
		var __createBinding = (Utils$3 && Utils$3.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Utils$3 && Utils$3.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireModeFiltering(), exports);
		__exportStar(requireDrawingOnlyNetProcessor(), exports);
		__exportStar(requireVirtualNetProcessor(), exports);
		
	} (Utils$3));
	return Utils$3;
}

var hasRequiredDesignModels;

function requireDesignModels () {
	if (hasRequiredDesignModels) return DesignModels;
	hasRequiredDesignModels = 1;
	(function (exports) {
		var __createBinding = (DesignModels && DesignModels.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (DesignModels && DesignModels.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SPICE = void 0;
		exports.SPICE = requireSPICE();
		__exportStar(requireBoundaryAnalysisMode(), exports);
		__exportStar(requireBoundaryAssertionStrength(), exports);
		__exportStar(requireBoundaryCharacteristic(), exports);
		__exportStar(requireBoundaryLevel(), exports);
		__exportStar(requireBoundaryRole(), exports);
		__exportStar(requireBoundaryTarget(), exports);
		__exportStar(requireCircuitStates(), exports);
		__exportStar(requireComponentBoundary(), exports);
		__exportStar(requireComponentModelParameterDefinition(), exports);
		__exportStar(requireDesignConfiguration(), exports);
		__exportStar(requireFunctionalModels(), exports);
		__exportStar(requireInferenceReports(), exports);
		__exportStar(requireISubModelSignalMapItemBase(), exports);
		__exportStar(requireMergeInferenceReports(), exports);
		__exportStar(requireMessageResult(), exports);
		__exportStar(requireMigration(), exports);
		__exportStar(requireModuleResolution(), exports);
		__exportStar(requireNumericalBoundary(), exports);
		__exportStar(requireRelativeSignal(), exports);
		__exportStar(requireSIUnitDeclaration(), exports);
		__exportStar(requireComponentSpiceModelDefinition(), exports);
		__exportStar(requireVarianceTypes(), exports);
		__exportStar(requireBoundaryAssertionState(), exports);
		__exportStar(requireBoundaryAssertionType(), exports);
		__exportStar(requireBoundaryClass(), exports);
		__exportStar(requireBoundaryDefinitionTypes(), exports);
		__exportStar(requireBoundaryErrorsDefinition(), exports);
		__exportStar(requireBoundarySourceType(), exports);
		__exportStar(requireCadSemanticFileType(), exports);
		__exportStar(requireCadTool(), exports);
		__exportStar(requireComponent(), exports);
		__exportStar(requireComponentDefinition(), exports);
		__exportStar(requireComponentDefinitionWithBoundaries(), exports);
		__exportStar(requireComponentMergeUtils(), exports);
		__exportStar(requireComponentWithBoundaries(), exports);
		__exportStar(requireCoreModel(), exports);
		__exportStar(requireDesign(), exports);
		__exportStar(requireDesignFileNetInfo(), exports);
		__exportStar(requireDesignHierarchy(), exports);
		__exportStar(requireDrawingModels(), exports);
		__exportStar(requireFonts$1(), exports);
		__exportStar(requireGlobalNetInfo(), exports);
		__exportStar(requireIBoundaryBase(), exports);
		__exportStar(requireIBoundaryDefinition(), exports);
		__exportStar(requireIBoundaryInstance(), exports);
		__exportStar(requireIConnectionBoundaryDefinition(), exports);
		__exportStar(requireIModeCondition(), exports);
		__exportStar(requireIModePrecondition(), exports);
		__exportStar(requireIModeStateConfig(), exports);
		__exportStar(requireIModelInfoBase(), exports);
		__exportStar(requireINumericalBoundaryDefinition(), exports);
		__exportStar(requireLibraryModel(), exports);
		__exportStar(requireMetadata(), exports);
		__exportStar(requireModeBase(), exports);
		__exportStar(requireModeBoundaryCondition(), exports);
		__exportStar(requireModelBase(), exports);
		__exportStar(requireModelWithComponents(), exports);
		__exportStar(requireModelVersion(), exports);
		__exportStar(requireModelWithNetStats(), exports);
		__exportStar(requireModeReferenceCondition(), exports);
		__exportStar(requireModeSignalLogicalStateCondition(), exports);
		__exportStar(requireModeStateType(), exports);
		__exportStar(requireModulePropertiesCacheFile(), exports);
		__exportStar(requireComponentsCacheFile(), exports);
		__exportStar(requireModulesCacheFile(), exports);
		__exportStar(requireNetInfo(), exports);
		__exportStar(requireNets(), exports);
		__exportStar(requireNetsCacheFile(), exports);
		__exportStar(requireParameterValue(), exports);
		__exportStar(requireParametrics(), exports);
		__exportStar(requirePcb(), exports);
		__exportStar(requirePowerSignalType(), exports);
		__exportStar(requireProjectDefinition(), exports);
		__exportStar(requireProjectFontConfiguration(), exports);
		__exportStar(requireNetRef(), exports);
		__exportStar(requireProjectDataTypeInstance(), exports);
		__exportStar(requireProjectInstanceContext(), exports);
		__exportStar(requireProjectModel$1(), exports);
		__exportStar(requireProjectModelManifest(), exports);
		__exportStar(requireProjectModelNetStats(), exports);
		__exportStar(requireProjectModule(), exports);
		__exportStar(requireRelativeDefinition(), exports);
		__exportStar(requireRuleFailure(), exports);
		__exportStar(requireSchematicModel(), exports);
		__exportStar(requireSchematicModelPort(), exports);
		__exportStar(requireSchematicReference(), exports);
		__exportStar(requireSignal(), exports);
		__exportStar(requireSignalAttributes(), exports);
		__exportStar(requireSignalBlockInfo(), exports);
		__exportStar(requireSignalDirection(), exports);
		__exportStar(requireSignalDriveMode(), exports);
		__exportStar(requireSignalFloatingBehavior(), exports);
		__exportStar(requireSignalId(), exports);
		__exportStar(requireSignalInstance(), exports);
		__exportStar(requireSignalStructuralBase(), exports);
		__exportStar(requireSignalTypeDeprecated(), exports);
		__exportStar(requireStandardComponentModel(), exports);
		__exportStar(requireUtils$3(), exports);
		
	} (DesignModels));
	return DesignModels;
}

var ModeStateConfigGeneration = {};

var ModeStateConfigGenerator = {};

var hasRequiredModeStateConfigGenerator;

function requireModeStateConfigGenerator () {
	if (hasRequiredModeStateConfigGenerator) return ModeStateConfigGenerator;
	hasRequiredModeStateConfigGenerator = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModeStateConfigGenerator = exports.IControlSignalClassDefinitionSchema = exports.ControlSignalClassSchema = exports.ControlSignalClass = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryLevel_1 = requireBoundaryLevel();
		const IModeStateConfig_1 = requireIModeStateConfig();
		const ModeStateType_1 = requireModeStateType();
		const ModeSignalLogicalStateCondition_1 = requireModeSignalLogicalStateCondition();
		const SignalDirection_1 = requireSignalDirection();
		const SignalDriveMode_1 = requireSignalDriveMode();
		var ControlSignalClass;
		(function (ControlSignalClass) {
		    ControlSignalClass["Reset"] = "reset";
		    ControlSignalClass["Enable"] = "enable";
		    ControlSignalClass["Uvlo"] = "uvlo";
		})(ControlSignalClass || (exports.ControlSignalClass = ControlSignalClass = {}));
		exports.ControlSignalClassSchema = zod_1.z.nativeEnum(ControlSignalClass);
		exports.IControlSignalClassDefinitionSchema = zod_1.z.object({
		    classId: exports.ControlSignalClassSchema,
		    aliases: zod_1.z.set(zod_1.z.string()),
		    stateConfig: IModeStateConfig_1.IModeStateConfigSchema,
		});
		class ModeStateConfigGenerator {
		    static { this.ID_PREFIX = 'gen-signal-logic'; }
		    static { this.RESET_STATE_CONFIG = {
		        states: [
		            { stateId: ModeStateType_1.ModeStateType.LogicLow, isNominalState: true, unreachableSeverity: BoundaryLevel_1.BoundaryLevel.Error },
		            { stateId: ModeStateType_1.ModeStateType.LogicHigh, unreachableSeverity: BoundaryLevel_1.BoundaryLevel.Warning },
		        ],
		    }; }
		    static { this.ENABLE_LIKE_STATE_CONFIG = {
		        states: [
		            { stateId: ModeStateType_1.ModeStateType.LogicHigh, isNominalState: true, unreachableSeverity: BoundaryLevel_1.BoundaryLevel.Error },
		            { stateId: ModeStateType_1.ModeStateType.LogicLow },
		        ],
		    }; }
		    static { this.CLASS_DEFINITIONS = [
		        {
		            classId: ControlSignalClass.Reset,
		            aliases: new Set(['RST', 'RESET', 'RESETB', 'RSTB']),
		            stateConfig: ModeStateConfigGenerator.RESET_STATE_CONFIG,
		        },
		        {
		            classId: ControlSignalClass.Enable,
		            aliases: new Set(['EN', 'ENA', 'ENABLE']),
		            stateConfig: ModeStateConfigGenerator.ENABLE_LIKE_STATE_CONFIG,
		        },
		        {
		            classId: ControlSignalClass.Uvlo,
		            aliases: new Set(['UVLO', 'UVIN', 'VUVLO', 'UV']),
		            stateConfig: ModeStateConfigGenerator.ENABLE_LIKE_STATE_CONFIG,
		        },
		    ]; }
		    static normalizeControlSignalToken(name) {
		        let token = name.trim().toUpperCase();
		        token = token.replace(/^[/!~#\\]+/, '');
		        token = token.replace(/[#/]+$/, '');
		        token = token.replace(/[_-](?:N|B|L)$/, '');
		        const segments = token.split(/[-_/.]+/).filter(Boolean);
		        if (segments.length > 0) {
		            token = segments[segments.length - 1];
		        }
		        token = token.replace(/[^A-Z0-9]/g, '');
		        token = token.replace(/\d+$/, '');
		        if (/^N[A-Z]{2,}$/.test(token)) {
		            token = token.slice(1);
		        }
		        return token;
		    }
		    static classifyControlSignal(signalName) {
		        return ModeStateConfigGenerator.findClassDefinition(signalName)?.classId;
		    }
		    static generateModeStateForSignal(signal, existingModes) {
		        if (!ModeStateConfigGenerator.isControlInputCandidate(signal)) {
		            return undefined;
		        }
		        const classDefinition = ModeStateConfigGenerator.findClassDefinition(signal.name);
		        if (!classDefinition) {
		            return undefined;
		        }
		        const alreadyDerivedFromSignal = existingModes.some((mode) => (mode.preconditions ?? []).some((precondition) => (0, ModeSignalLogicalStateCondition_1.isModeSignalLogicalStateCondition)(precondition.condition) &&
		            String(precondition.condition.signalId) === String(signal.id)));
		        if (alreadyDerivedFromSignal) {
		            return undefined;
		        }
		        const id = ModeStateConfigGenerator.buildGeneratedModeId(classDefinition.classId, signal);
		        if (existingModes.some((mode) => mode.id === id)) {
		            return undefined;
		        }
		        return {
		            id,
		            preconditions: [{ id: 1, condition: { signalId: signal.id } }],
		            stateConfig: ModeStateConfigGenerator.copyStateConfig(classDefinition.stateConfig),
		        };
		    }
		    static generateModeStatesForComponent(component) {
		        const generated = [];
		        const knownModes = [
		            ...component.modes,
		            ...(component.model?.inferenceReport?.modes?.map((entry) => entry.mode) ?? []),
		        ];
		        for (const signal of component.signals) {
		            const mode = ModeStateConfigGenerator.generateModeStateForSignal(signal, knownModes);
		            if (mode) {
		                generated.push(mode);
		                knownModes.push(mode);
		            }
		        }
		        return generated;
		    }
		    static isControlInputCandidate(signal) {
		        if (signal.signalDirection !== SignalDirection_1.SignalDirection.Input) {
		            return false;
		        }
		        if (signal.attributes?.digital !== true) {
		            return false;
		        }
		        const driveModes = signal.supportedDriveModes ?? [];
		        if (driveModes.length === 0) {
		            return false;
		        }
		        if (!driveModes.every((driveMode) => driveMode.type === SignalDriveMode_1.SignalInputDriveType.HighZ)) {
		            return false;
		        }
		        return driveModes.some((driveMode) => driveMode.parameters?.inputHighVoltage !== undefined && driveMode.parameters?.inputLowVoltage !== undefined);
		    }
		    static findClassDefinition(signalName) {
		        const token = ModeStateConfigGenerator.normalizeControlSignalToken(signalName);
		        return ModeStateConfigGenerator.CLASS_DEFINITIONS.find((definition) => definition.aliases.has(token));
		    }
		    static buildGeneratedModeId(classId, signal) {
		        return `${ModeStateConfigGenerator.ID_PREFIX}:${classId}:${signal.id}`;
		    }
		    static copyStateConfig(template) {
		        return { states: template.states.map((state) => ({ ...state })) };
		    }
		}
		exports.ModeStateConfigGenerator = ModeStateConfigGenerator;
		
	} (ModeStateConfigGenerator));
	return ModeStateConfigGenerator;
}

var hasRequiredModeStateConfigGeneration;

function requireModeStateConfigGeneration () {
	if (hasRequiredModeStateConfigGeneration) return ModeStateConfigGeneration;
	hasRequiredModeStateConfigGeneration = 1;
	(function (exports) {
		var __createBinding = (ModeStateConfigGeneration && ModeStateConfigGeneration.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ModeStateConfigGeneration && ModeStateConfigGeneration.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireModeStateConfigGenerator(), exports);
		
	} (ModeStateConfigGeneration));
	return ModeStateConfigGeneration;
}

var Files = {};

var IResolvedComponentsFile = {};

var hasRequiredIResolvedComponentsFile;

function requireIResolvedComponentsFile () {
	if (hasRequiredIResolvedComponentsFile) return IResolvedComponentsFile;
	hasRequiredIResolvedComponentsFile = 1;
	Object.defineProperty(IResolvedComponentsFile, "__esModule", { value: true });
	
	return IResolvedComponentsFile;
}

var filenames = {};

var hasRequiredFilenames;

function requireFilenames () {
	if (hasRequiredFilenames) return filenames;
	hasRequiredFilenames = 1;
	Object.defineProperty(filenames, "__esModule", { value: true });
	filenames.COMPONENT_FUNCTION_STATES_NAME = filenames.MODE_STATES_NAME = filenames.NETSTATS_NAME = filenames.DESIGN_CONFIG_NAME = filenames.RESOLVED_COMPONENTS_NAME = filenames.PROJECT_MODEL_NAME = void 0;
	filenames.PROJECT_MODEL_NAME = 'project.model.json';
	filenames.RESOLVED_COMPONENTS_NAME = 'resolved-components.json';
	filenames.DESIGN_CONFIG_NAME = 'design-configuration.json';
	filenames.NETSTATS_NAME = 'netstats.json';
	filenames.MODE_STATES_NAME = 'mode-states.json';
	filenames.COMPONENT_FUNCTION_STATES_NAME = 'component-function-states.json';
	
	return filenames;
}

var pathUtils = {};

var hasRequiredPathUtils$1;

function requirePathUtils$1 () {
	if (hasRequiredPathUtils$1) return pathUtils;
	hasRequiredPathUtils$1 = 1;
	Object.defineProperty(pathUtils, "__esModule", { value: true });
	pathUtils.normalizePath = normalizePath;
	pathUtils.joinPath = joinPath;
	pathUtils.resolvePath = resolvePath;
	pathUtils.dirnamePath = dirnamePath;
	pathUtils.basenamePath = basenamePath;
	pathUtils.extnamePath = extnamePath;
	pathUtils.isAbsolutePath = isAbsolutePath;
	pathUtils.relativePath = relativePath;
	function normalizePath(path) {
	    if (path.length === 0) {
	        return '.';
	    }
	    const isAbsolute = path.startsWith('/');
	    const trailingSlash = path.endsWith('/');
	    const segments = path.split('/').filter((segment) => segment.length > 0);
	    const resultSegments = [];
	    for (const segment of segments) {
	        if (segment === '.') {
	            continue;
	        }
	        if (segment === '..') {
	            if (resultSegments.length > 0 && resultSegments[resultSegments.length - 1] !== '..') {
	                resultSegments.pop();
	            }
	            else if (!isAbsolute) {
	                resultSegments.push('..');
	            }
	        }
	        else {
	            resultSegments.push(segment);
	        }
	    }
	    let result = resultSegments.join('/');
	    if (isAbsolute) {
	        result = '/' + result;
	    }
	    if (trailingSlash && result !== '/' && resultSegments.length > 0) {
	        result = result + '/';
	    }
	    if (result.length === 0) {
	        return isAbsolute ? '/' : '.';
	    }
	    return result;
	}
	function joinPath(...paths) {
	    if (paths.length === 0) {
	        return '.';
	    }
	    const joined = paths.filter((p) => p.length > 0).join('/');
	    return normalizePath(joined);
	}
	function resolvePath(cwd, ...paths) {
	    let resolvedPath = '';
	    for (let i = paths.length - 1; i >= 0; i--) {
	        const path = paths[i];
	        if (path.length === 0) {
	            continue;
	        }
	        resolvedPath = resolvedPath.length > 0 ? path + '/' + resolvedPath : path;
	        if (path.startsWith('/')) {
	            return normalizePath(resolvedPath);
	        }
	    }
	    if (cwd.length > 0) {
	        resolvedPath = resolvedPath.length > 0 ? cwd + '/' + resolvedPath : cwd;
	    }
	    if (!resolvedPath.startsWith('/')) {
	        resolvedPath = '/' + resolvedPath;
	    }
	    return normalizePath(resolvedPath);
	}
	function dirnamePath(path) {
	    if (path.length === 0) {
	        return '.';
	    }
	    const normalized = normalizePath(path);
	    if (normalized === '/') {
	        return '/';
	    }
	    const withoutTrailingSlash = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
	    const lastSlashIndex = withoutTrailingSlash.lastIndexOf('/');
	    if (lastSlashIndex === -1) {
	        return '.';
	    }
	    if (lastSlashIndex === 0) {
	        return '/';
	    }
	    return withoutTrailingSlash.slice(0, lastSlashIndex);
	}
	function basenamePath(path) {
	    if (path.length === 0) {
	        return '';
	    }
	    const normalized = normalizePath(path);
	    if (normalized === '/') {
	        return '';
	    }
	    const withoutTrailingSlash = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
	    const lastSlashIndex = withoutTrailingSlash.lastIndexOf('/');
	    if (lastSlashIndex === -1) {
	        return withoutTrailingSlash;
	    }
	    return withoutTrailingSlash.slice(lastSlashIndex + 1);
	}
	function extnamePath(path) {
	    const base = basenamePath(path);
	    if (base.length === 0) {
	        return '';
	    }
	    const dotIndex = base.lastIndexOf('.');
	    if (dotIndex <= 0) {
	        return '';
	    }
	    return base.slice(dotIndex);
	}
	function isAbsolutePath(path) {
	    return path.startsWith('/');
	}
	function relativePath(from, to) {
	    const normalizedFrom = normalizePath(from);
	    const normalizedTo = normalizePath(to);
	    if (normalizedFrom === normalizedTo) {
	        return '';
	    }
	    const fromParts = normalizedFrom.split('/').filter((p) => p.length > 0);
	    const toParts = normalizedTo.split('/').filter((p) => p.length > 0);
	    let commonLength = 0;
	    const minLength = Math.min(fromParts.length, toParts.length);
	    for (let i = 0; i < minLength; i++) {
	        if (fromParts[i] === toParts[i]) {
	            commonLength++;
	        }
	        else {
	            break;
	        }
	    }
	    const upCount = fromParts.length - commonLength;
	    const upParts = new Array(upCount).fill('..');
	    const downParts = toParts.slice(commonLength);
	    const result = [...upParts, ...downParts].join('/');
	    return result.length > 0 ? result : '.';
	}
	
	return pathUtils;
}

var uploadTypes = {};

var hasRequiredUploadTypes;

function requireUploadTypes () {
	if (hasRequiredUploadTypes) return uploadTypes;
	hasRequiredUploadTypes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.UploadValidationErrorResponseSchema = exports.UploadResultSchema = exports.UploadedFileSchema = exports.ValidationWarningSchema = exports.ValidationErrorSchema = exports.ValidationWarningCode = exports.ValidationErrorCode = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const CadTool_1 = requireCadTool();
		exports.ValidationErrorCode = zod_1.z.enum([
		    'FILE_TOO_LARGE',
		    'TOTAL_SIZE_EXCEEDED',
		    'TOO_MANY_FILES',
		    'INVALID_FILENAME',
		    'PATH_TRAVERSAL',
		    'PATH_TOO_DEEP',
		    'PATH_TOO_LONG',
		    'BLOCKED_EXTENSION',
		    'MISSING_REQUIRED_FILE',
		    'UNRECOGNIZED_PROJECT_TYPE',
		    'MIXED_PROJECT_TYPES',
		]);
		exports.ValidationWarningCode = zod_1.z.enum(['EMPTY_DIRECTORY', 'UNUSUAL_STRUCTURE']);
		exports.ValidationErrorSchema = zod_1.z.object({
		    path: zod_1.z.string(),
		    code: exports.ValidationErrorCode,
		    message: zod_1.z.string(),
		});
		exports.ValidationWarningSchema = zod_1.z.object({
		    path: zod_1.z.string(),
		    code: exports.ValidationWarningCode,
		    message: zod_1.z.string(),
		});
		exports.UploadedFileSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    relativePath: zod_1.z.string(),
		    size: zod_1.z.number(),
		    mimeType: zod_1.z.string(),
		});
		exports.UploadResultSchema = zod_1.z.object({
		    success: zod_1.z.literal(true),
		    uploadId: zod_1.z.string(),
		    files: zod_1.z.array(exports.UploadedFileSchema),
		    cadTool: CadTool_1.CadToolSchema.nullable(),
		    warnings: zod_1.z.array(exports.ValidationWarningSchema),
		});
		exports.UploadValidationErrorResponseSchema = zod_1.z.object({
		    success: zod_1.z.literal(false),
		    errors: zod_1.z.array(exports.ValidationErrorSchema),
		    warnings: zod_1.z.array(exports.ValidationWarningSchema),
		});
		
	} (uploadTypes));
	return uploadTypes;
}

var uploadConstants = {};

var hasRequiredUploadConstants;

function requireUploadConstants () {
	if (hasRequiredUploadConstants) return uploadConstants;
	hasRequiredUploadConstants = 1;
	Object.defineProperty(uploadConstants, "__esModule", { value: true });
	uploadConstants.ARCHIVE_LIMITS = uploadConstants.BLOCKED_DIRECTORIES = uploadConstants.UPLOAD_LIMITS = void 0;
	uploadConstants.UPLOAD_LIMITS = {
	    MAX_FILES: 10000,
	    MAX_TOTAL_MB: 20 * 1024,
	    MAX_TOTAL_BYTES: 20 * 1024 * 1024 * 1024,
	    MAX_FILE_SIZE_MB: 100,
	    MAX_FILE_SIZE_BYTES: 100 * 1024 * 1024,
	    MAX_PATH_DEPTH: 10,
	    MAX_PATH_LENGTH: 500,
	};
	uploadConstants.BLOCKED_DIRECTORIES = [
	    '.git',
	    '.svn',
	    '.hg',
	    'node_modules',
	    '__pycache__',
	    '.DS_Store',
	    '__MACOSX',
	];
	uploadConstants.ARCHIVE_LIMITS = {
	    MAX_EXPANDED_BYTES: 512 * 1024 * 1024,
	    MAX_COMPRESSION_RATIO: 100,
	};
	
	return uploadConstants;
}

var uploadValidators = {};

var hasRequiredUploadValidators;

function requireUploadValidators () {
	if (hasRequiredUploadValidators) return uploadValidators;
	hasRequiredUploadValidators = 1;
	Object.defineProperty(uploadValidators, "__esModule", { value: true });
	uploadValidators.validateFilename = validateFilename;
	uploadValidators.validatePath = validatePath;
	uploadValidators.validateFileSize = validateFileSize;
	uploadValidators.validateExtension = validateExtension;
	uploadValidators.validateFileMetadata = validateFileMetadata;
	const upload_constants_1 = requireUploadConstants();
	function validateFilename(filename) {
	    const errors = [];
	    if (filename.includes('\x00')) {
	        errors.push({
	            path: filename,
	            code: 'INVALID_FILENAME',
	            message: 'Filename contains null bytes',
	        });
	    }
	    if (/[\x00-\x1F]/.test(filename)) {
	        errors.push({
	            path: filename,
	            code: 'INVALID_FILENAME',
	            message: 'Filename contains control characters',
	        });
	    }
	    if (filename.includes('..')) {
	        errors.push({
	            path: filename,
	            code: 'PATH_TRAVERSAL',
	            message: 'Filename contains path traversal pattern',
	        });
	    }
	    const invalidChars = /[<>:"|?*]/;
	    if (invalidChars.test(filename)) {
	        errors.push({
	            path: filename,
	            code: 'INVALID_FILENAME',
	            message: 'Filename contains invalid characters',
	        });
	    }
	    return errors;
	}
	function validatePath(path) {
	    const errors = [];
	    if (path.includes('..')) {
	        errors.push({
	            path,
	            code: 'PATH_TRAVERSAL',
	            message: 'Path contains traversal pattern',
	        });
	    }
	    const pathParts = path.split('/');
	    for (const part of pathParts) {
	        if (upload_constants_1.BLOCKED_DIRECTORIES.includes(part)) {
	            errors.push({
	                path,
	                code: 'PATH_TRAVERSAL',
	                message: `Path contains blocked directory: ${part}`,
	            });
	            break;
	        }
	    }
	    if (pathParts.length > upload_constants_1.UPLOAD_LIMITS.MAX_PATH_DEPTH) {
	        errors.push({
	            path,
	            code: 'PATH_TOO_DEEP',
	            message: `Path depth exceeds maximum of ${upload_constants_1.UPLOAD_LIMITS.MAX_PATH_DEPTH}`,
	        });
	    }
	    if (path.length > upload_constants_1.UPLOAD_LIMITS.MAX_PATH_LENGTH) {
	        errors.push({
	            path,
	            code: 'PATH_TOO_LONG',
	            message: `Path length exceeds maximum of ${upload_constants_1.UPLOAD_LIMITS.MAX_PATH_LENGTH}`,
	        });
	    }
	    return errors;
	}
	function validateFileSize(size, filePath = '') {
	    const errors = [];
	    if (size > upload_constants_1.UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES) {
	        errors.push({
	            path: filePath,
	            code: 'FILE_TOO_LARGE',
	            message: `File size ${size} exceeds maximum of ${upload_constants_1.UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES}`,
	        });
	    }
	    return errors;
	}
	function validateExtension(_filename, _cadTool) {
	    return [];
	}
	function validateFileMetadata(params) {
	    const errors = [];
	    const { relativePath, size, cadTool } = params;
	    errors.push(...validatePath(relativePath));
	    const filename = relativePath.split('/').pop() ?? '';
	    errors.push(...validateFilename(filename));
	    errors.push(...validateFileSize(size, relativePath));
	    errors.push(...validateExtension());
	    return errors;
	}
	
	return uploadValidators;
}

var toolGeneratedArtifacts = {};

var hasRequiredToolGeneratedArtifacts;

function requireToolGeneratedArtifacts () {
	if (hasRequiredToolGeneratedArtifacts) return toolGeneratedArtifacts;
	hasRequiredToolGeneratedArtifacts = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TOOL_GENERATED_ARTIFACTS = void 0;
		exports.isToolGeneratedArtifact = isToolGeneratedArtifact;
		exports.TOOL_GENERATED_ARTIFACTS = {
		    altium: [{ directory: 'history', extensions: ['.zip', '.log'] }],
		    xpedition: [
		        { directory: 'projectbackup', extensions: ['.zip'] },
		        { directory: 'cdbback', extensions: ['.zip'] },
		        { directory: 'pcb', extensions: ['.prj'] },
		    ],
		};
		function isToolGeneratedArtifact(relativePath, tool) {
		    if (tool === null) {
		        return false;
		    }
		    const rules = exports.TOOL_GENERATED_ARTIFACTS[tool];
		    if (rules === undefined) {
		        return false;
		    }
		    const segments = relativePath.split('/');
		    const fileName = segments.at(-1)?.toLowerCase() ?? '';
		    const directories = segments.slice(0, -1).map((segment) => segment.toLowerCase());
		    return rules.some((rule) => directories.includes(rule.directory) && rule.extensions.some((extension) => fileName.endsWith(extension)));
		}
		
	} (toolGeneratedArtifacts));
	return toolGeneratedArtifacts;
}

var fileCollectionValidationTypes = {};

var hasRequiredFileCollectionValidationTypes;

function requireFileCollectionValidationTypes () {
	if (hasRequiredFileCollectionValidationTypes) return fileCollectionValidationTypes;
	hasRequiredFileCollectionValidationTypes = 1;
	Object.defineProperty(fileCollectionValidationTypes, "__esModule", { value: true });
	fileCollectionValidationTypes.FileCollectionValidationErrorType = void 0;
	var FileCollectionValidationErrorType;
	(function (FileCollectionValidationErrorType) {
	    FileCollectionValidationErrorType["FileTooLarge"] = "file-too-large";
	    FileCollectionValidationErrorType["TotalSizeExceeded"] = "total-size-exceeded";
	    FileCollectionValidationErrorType["TooManyFiles"] = "too-many-files";
	    FileCollectionValidationErrorType["InvalidFilename"] = "invalid-filename";
	    FileCollectionValidationErrorType["PathTraversal"] = "path-traversal";
	    FileCollectionValidationErrorType["PathTooDeep"] = "path-too-deep";
	    FileCollectionValidationErrorType["PathTooLong"] = "path-too-long";
	    FileCollectionValidationErrorType["BlockedExtension"] = "blocked-extension";
	    FileCollectionValidationErrorType["FileValidationFailed"] = "file-validation-failed";
	    FileCollectionValidationErrorType["UnrecognizedFileType"] = "unrecognized-file-type";
	    FileCollectionValidationErrorType["ForeignToolFile"] = "foreign-tool-file";
	    FileCollectionValidationErrorType["MixedProjectTypes"] = "mixed-project-types";
	    FileCollectionValidationErrorType["MultipleProjectsDetected"] = "multiple-projects-detected";
	    FileCollectionValidationErrorType["UnrecognizedProjectType"] = "unrecognized-project-type";
	    FileCollectionValidationErrorType["MissingReferencedFile"] = "missing-referenced-file";
	    FileCollectionValidationErrorType["MissingRequiredFile"] = "missing-required-file";
	    FileCollectionValidationErrorType["InconsistentStructureFile"] = "inconsistent-structure-file";
	    FileCollectionValidationErrorType["UnpairedStructureFile"] = "unpaired-structure-file";
	    FileCollectionValidationErrorType["NetlistExcludedFromTypeDetection"] = "netlist-excluded-from-type-detection";
	})(FileCollectionValidationErrorType || (fileCollectionValidationTypes.FileCollectionValidationErrorType = FileCollectionValidationErrorType = {}));
	
	return fileCollectionValidationTypes;
}

var hasRequiredFiles;

function requireFiles () {
	if (hasRequiredFiles) return Files;
	hasRequiredFiles = 1;
	(function (exports) {
		var __createBinding = (Files && Files.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Files && Files.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FileNames = void 0;
		__exportStar(requireIResolvedComponentsFile(), exports);
		exports.FileNames = requireFilenames();
		__exportStar(requirePathUtils$1(), exports);
		__exportStar(requireUploadTypes(), exports);
		__exportStar(requireUploadConstants(), exports);
		__exportStar(requireUploadValidators(), exports);
		__exportStar(requireToolGeneratedArtifacts(), exports);
		__exportStar(requirePathUtils$1(), exports);
		__exportStar(requireFileCollectionValidationTypes(), exports);
		
	} (Files));
	return Files;
}

var Markdown = {};

var MarkdownDirectiveDefinition = {};

var hasRequiredMarkdownDirectiveDefinition;

function requireMarkdownDirectiveDefinition () {
	if (hasRequiredMarkdownDirectiveDefinition) return MarkdownDirectiveDefinition;
	hasRequiredMarkdownDirectiveDefinition = 1;
	Object.defineProperty(MarkdownDirectiveDefinition, "__esModule", { value: true });
	MarkdownDirectiveDefinition.NodeTypes = void 0;
	MarkdownDirectiveDefinition.escapeDirectiveContent = escapeDirectiveContent;
	MarkdownDirectiveDefinition.quoteDirectiveAttribute = quoteDirectiveAttribute;
	var NodeTypes;
	(function (NodeTypes) {
	    NodeTypes["TextDirective"] = "textDirective";
	    NodeTypes["LeafDirective"] = "leafDirective";
	    NodeTypes["ContainerDirective"] = "containerDirective";
	    NodeTypes["AllDirectives"] = "__all";
	})(NodeTypes || (MarkdownDirectiveDefinition.NodeTypes = NodeTypes = {}));
	function escapeDirectiveContent(value) {
	    return value.replace(/([\\[\]])/g, '\\$1');
	}
	function quoteDirectiveAttribute(value) {
	    return value.includes('"') ? `'${value}'` : `"${value}"`;
	}
	
	return MarkdownDirectiveDefinition;
}

var ClauseReference = {};

var hasRequiredClauseReference;

function requireClauseReference () {
	if (hasRequiredClauseReference) return ClauseReference;
	hasRequiredClauseReference = 1;
	Object.defineProperty(ClauseReference, "__esModule", { value: true });
	ClauseReference.ClauseReferenceMarkdownDirectiveDefinition = void 0;
	ClauseReference.parseClauseReferenceLabel = parseClauseReferenceLabel;
	const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
	ClauseReference.ClauseReferenceMarkdownDirectiveDefinition = Object.freeze({
	    allowedAttributes: ['label'],
	    contentAttributeName: 'label',
	    directive: 'clause',
	    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
	});
	const CLAUSE_REFERENCE_PATTERN = /^([a-z][a-z0-9_]*)\s*#\s*(\d+)(?:\s+v(\d+))?$/i;
	function parseClauseReferenceLabel(label) {
	    const match = CLAUSE_REFERENCE_PATTERN.exec(label.trim());
	    if (match === null) {
	        return undefined;
	    }
	    const [, propertyKey, clauseNumber, version] = match;
	    if (propertyKey === undefined || clauseNumber === undefined) {
	        return undefined;
	    }
	    return {
	        propertyKey: propertyKey.toLowerCase(),
	        clauseNumber: Number(clauseNumber),
	        version: version === undefined ? null : Number(version),
	    };
	}
	
	return ClauseReference;
}

var ModuleReference = {};

var hasRequiredModuleReference;

function requireModuleReference () {
	if (hasRequiredModuleReference) return ModuleReference;
	hasRequiredModuleReference = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModuleReferenceMarkdownDirectiveDefinition = void 0;
		exports.formatModuleReferenceDirective = formatModuleReferenceDirective;
		const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
		exports.ModuleReferenceMarkdownDirectiveDefinition = Object.freeze({
		    allowedAttributes: ['moduleRef', 'signal'],
		    contentAttributeName: 'moduleRef',
		    directive: 'module',
		    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
		    editorMarker: '$',
		});
		function formatModuleReferenceDirective({ moduleRef, signal }) {
		    const head = `:${exports.ModuleReferenceMarkdownDirectiveDefinition.directive}[${(0, MarkdownDirectiveDefinition_1.escapeDirectiveContent)(moduleRef)}]`;
		    if (signal === undefined || signal === null || signal === '') {
		        return head;
		    }
		    return `${head}{signal=${(0, MarkdownDirectiveDefinition_1.quoteDirectiveAttribute)(signal)}}`;
		}
		
	} (ModuleReference));
	return ModuleReference;
}

var NetReference = {};

var hasRequiredNetReference;

function requireNetReference () {
	if (hasRequiredNetReference) return NetReference;
	hasRequiredNetReference = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetReferenceMarkdownDirectiveDefinition = void 0;
		exports.formatNetReferenceDirective = formatNetReferenceDirective;
		const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
		exports.NetReferenceMarkdownDirectiveDefinition = Object.freeze({
		    allowedAttributes: ['netName'],
		    contentAttributeName: 'netName',
		    directive: 'net',
		    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
		    editorMarker: '#',
		});
		function formatNetReferenceDirective(netName) {
		    return `:${exports.NetReferenceMarkdownDirectiveDefinition.directive}[${(0, MarkdownDirectiveDefinition_1.escapeDirectiveContent)(netName)}]`;
		}
		
	} (NetReference));
	return NetReference;
}

var ParametersAndModesTable = {};

var hasRequiredParametersAndModesTable;

function requireParametersAndModesTable () {
	if (hasRequiredParametersAndModesTable) return ParametersAndModesTable;
	hasRequiredParametersAndModesTable = 1;
	Object.defineProperty(ParametersAndModesTable, "__esModule", { value: true });
	ParametersAndModesTable.ParametersAndModesRowKindSchema = ParametersAndModesTable.ParametersAndModesRowKind = ParametersAndModesTable.PARAMETERS_AND_MODES_TABLE_DIRECTIVE = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	ParametersAndModesTable.PARAMETERS_AND_MODES_TABLE_DIRECTIVE = 'parameters-and-modes-table';
	var ParametersAndModesRowKind;
	(function (ParametersAndModesRowKind) {
	    ParametersAndModesRowKind["Parameter"] = "parameter";
	    ParametersAndModesRowKind["Mode"] = "mode";
	})(ParametersAndModesRowKind || (ParametersAndModesTable.ParametersAndModesRowKind = ParametersAndModesRowKind = {}));
	ParametersAndModesTable.ParametersAndModesRowKindSchema = zod_1.z.nativeEnum(ParametersAndModesRowKind);
	
	return ParametersAndModesTable;
}

var PdfCitation = {};

var hasRequiredPdfCitation;

function requirePdfCitation () {
	if (hasRequiredPdfCitation) return PdfCitation;
	hasRequiredPdfCitation = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PdfCitationMarkdownDirectiveDefinition = void 0;
		exports.parsePdfCitationElementIndexes = parsePdfCitationElementIndexes;
		exports.formatPdfCitationDirective = formatPdfCitationDirective;
		const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
		exports.PdfCitationMarkdownDirectiveDefinition = Object.freeze({
		    allowedAttributes: ['citationText', 'fileId', 'elementIndex', 'pageNumber', 'citedText', 'description'],
		    contentAttributeName: 'citationText',
		    directive: 'pdf-citation',
		    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
		});
		function parsePdfCitationElementIndexes(value) {
		    if (value === undefined || value === null) {
		        return null;
		    }
		    const parts = typeof value === 'number' ?
		        [value]
		        : value
		            .split(',')
		            .map((part) => part.trim())
		            .filter((part) => part !== '')
		            .map((part) => Number(part));
		    const indexes = parts.filter((part) => Number.isInteger(part) && part >= 0);
		    if (indexes.length === 0 || indexes.length !== parts.length) {
		        return null;
		    }
		    return [...new Set(indexes)].sort((left, right) => left - right);
		}
		function formatPdfCitationDirective({ citationText, fileId, elementIndex, elementIndexes, pageNumber, citedText, description, }) {
		    if (fileId.trim() === '') {
		        throw new Error('A PDF citation requires a fileId');
		    }
		    const normalizedCitedText = citedText?.trim();
		    const effectiveIndexes = elementIndexes !== undefined && elementIndexes !== null ? [...elementIndexes]
		        : elementIndex !== undefined && elementIndex !== null ? [elementIndex]
		            : null;
		    if (effectiveIndexes === null) {
		        if ((pageNumber === undefined || pageNumber === null) && !normalizedCitedText) {
		            throw new Error('A PDF citation requires an elementIndex, pageNumber, or citedText');
		        }
		    }
		    else if (effectiveIndexes.length === 0 || effectiveIndexes.some((index) => !Number.isInteger(index) || index < 0)) {
		        throw new Error('A PDF citation elementIndex must be a non-negative integer');
		    }
		    if (pageNumber !== undefined && pageNumber !== null && (!Number.isInteger(pageNumber) || pageNumber < 1)) {
		        throw new Error('A PDF citation pageNumber must be a positive integer');
		    }
		    const attributes = [`fileId=${(0, MarkdownDirectiveDefinition_1.quoteDirectiveAttribute)(fileId)}`];
		    if (effectiveIndexes !== null) {
		        const serializedIndexes = [...new Set(effectiveIndexes)].sort((left, right) => left - right).join(',');
		        attributes.push(serializedIndexes.includes(',') ?
		            `elementIndex=${(0, MarkdownDirectiveDefinition_1.quoteDirectiveAttribute)(serializedIndexes)}`
		            : `elementIndex=${serializedIndexes}`);
		    }
		    if (pageNumber !== undefined && pageNumber !== null) {
		        attributes.push(`pageNumber=${pageNumber}`);
		    }
		    if (normalizedCitedText) {
		        attributes.push(`citedText=${(0, MarkdownDirectiveDefinition_1.quoteDirectiveAttribute)(normalizedCitedText)}`);
		    }
		    if (description !== undefined && description !== null && description !== '') {
		        attributes.push(`description=${(0, MarkdownDirectiveDefinition_1.quoteDirectiveAttribute)(description)}`);
		    }
		    return `:${exports.PdfCitationMarkdownDirectiveDefinition.directive}[${(0, MarkdownDirectiveDefinition_1.escapeDirectiveContent)(citationText)}]{${attributes.join(' ')}}`;
		}
		
	} (PdfCitation));
	return PdfCitation;
}

var SeverityReference = {};

var severityNames = {};

var enums = {};

var hasRequiredEnums;

function requireEnums () {
	if (hasRequiredEnums) return enums;
	hasRequiredEnums = 1;
	Object.defineProperty(enums, "__esModule", { value: true });
	enums.AssertionDefinitionLifecycleSchema = enums.AssertionDefinitionLifecycle = enums.FindingContinuityDispositionSchema = enums.FindingContinuityDisposition = enums.FindingLinkOriginSchema = enums.FindingLinkOrigin = enums.ConfigScopeSchema = enums.ConfigScope = enums.LogEntryAuthorRoleSchema = enums.LogEntryAuthorRole = enums.LogEntryTypeSchema = enums.LogEntryType = enums.ContinuityKeyTypeSchema = enums.ContinuityKeyType = enums.RequirementWorkflowStateSchema = enums.RequirementWorkflowState = enums.NON_ACTIONABLE_REQUIREMENT_SEVERITIES = enums.ACTIONABLE_REQUIREMENT_SEVERITIES = enums.RequirementSeveritySchema = enums.RequirementSeverity = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var RequirementSeverity;
	(function (RequirementSeverity) {
	    RequirementSeverity[RequirementSeverity["NotApplicable"] = 100] = "NotApplicable";
	    RequirementSeverity[RequirementSeverity["Pending"] = 200] = "Pending";
	    RequirementSeverity[RequirementSeverity["Pass"] = 300] = "Pass";
	    RequirementSeverity[RequirementSeverity["Recommendation"] = 400] = "Recommendation";
	    RequirementSeverity[RequirementSeverity["Indeterminate"] = 500] = "Indeterminate";
	    RequirementSeverity[RequirementSeverity["Warning"] = 600] = "Warning";
	    RequirementSeverity[RequirementSeverity["Fail"] = 700] = "Fail";
	})(RequirementSeverity || (enums.RequirementSeverity = RequirementSeverity = {}));
	enums.RequirementSeveritySchema = zod_1.z.nativeEnum(RequirementSeverity);
	enums.ACTIONABLE_REQUIREMENT_SEVERITIES = [
	    RequirementSeverity.Recommendation,
	    RequirementSeverity.Warning,
	    RequirementSeverity.Fail,
	];
	enums.NON_ACTIONABLE_REQUIREMENT_SEVERITIES = [
	    RequirementSeverity.NotApplicable,
	    RequirementSeverity.Pending,
	    RequirementSeverity.Pass,
	    RequirementSeverity.Indeterminate,
	];
	var RequirementWorkflowState;
	(function (RequirementWorkflowState) {
	    RequirementWorkflowState[RequirementWorkflowState["Pending"] = 100] = "Pending";
	    RequirementWorkflowState[RequirementWorkflowState["Unreviewed"] = 200] = "Unreviewed";
	    RequirementWorkflowState[RequirementWorkflowState["Acknowledged"] = 300] = "Acknowledged";
	    RequirementWorkflowState[RequirementWorkflowState["Ignored"] = 400] = "Ignored";
	    RequirementWorkflowState[RequirementWorkflowState["Fixed"] = 500] = "Fixed";
	})(RequirementWorkflowState || (enums.RequirementWorkflowState = RequirementWorkflowState = {}));
	enums.RequirementWorkflowStateSchema = zod_1.z.nativeEnum(RequirementWorkflowState);
	var ContinuityKeyType;
	(function (ContinuityKeyType) {
	    ContinuityKeyType[ContinuityKeyType["Net"] = 100] = "Net";
	    ContinuityKeyType[ContinuityKeyType["Module"] = 200] = "Module";
	    ContinuityKeyType[ContinuityKeyType["ModuleNet"] = 300] = "ModuleNet";
	    ContinuityKeyType[ContinuityKeyType["Schematic"] = 400] = "Schematic";
	    ContinuityKeyType[ContinuityKeyType["Design"] = 500] = "Design";
	    ContinuityKeyType[ContinuityKeyType["Custom"] = 600] = "Custom";
	    ContinuityKeyType[ContinuityKeyType["NetConn"] = 700] = "NetConn";
	})(ContinuityKeyType || (enums.ContinuityKeyType = ContinuityKeyType = {}));
	enums.ContinuityKeyTypeSchema = zod_1.z.nativeEnum(ContinuityKeyType);
	var LogEntryType;
	(function (LogEntryType) {
	    LogEntryType[LogEntryType["Update"] = 100] = "Update";
	    LogEntryType[LogEntryType["Note"] = 200] = "Note";
	    LogEntryType[LogEntryType["DeterministicValidationResult"] = 300] = "DeterministicValidationResult";
	    LogEntryType[LogEntryType["AiValidationResult"] = 400] = "AiValidationResult";
	    LogEntryType[LogEntryType["Continuity"] = 500] = "Continuity";
	    LogEntryType[LogEntryType["Initialization"] = 600] = "Initialization";
	})(LogEntryType || (enums.LogEntryType = LogEntryType = {}));
	enums.LogEntryTypeSchema = zod_1.z.nativeEnum(LogEntryType);
	var LogEntryAuthorRole;
	(function (LogEntryAuthorRole) {
	    LogEntryAuthorRole[LogEntryAuthorRole["System"] = 100] = "System";
	    LogEntryAuthorRole[LogEntryAuthorRole["Admin"] = 200] = "Admin";
	    LogEntryAuthorRole[LogEntryAuthorRole["User"] = 300] = "User";
	})(LogEntryAuthorRole || (enums.LogEntryAuthorRole = LogEntryAuthorRole = {}));
	enums.LogEntryAuthorRoleSchema = zod_1.z.nativeEnum(LogEntryAuthorRole);
	var ConfigScope;
	(function (ConfigScope) {
	    ConfigScope[ConfigScope["Global"] = 100] = "Global";
	    ConfigScope[ConfigScope["Organization"] = 200] = "Organization";
	    ConfigScope[ConfigScope["Project"] = 300] = "Project";
	})(ConfigScope || (enums.ConfigScope = ConfigScope = {}));
	enums.ConfigScopeSchema = zod_1.z.nativeEnum(ConfigScope);
	var FindingLinkOrigin;
	(function (FindingLinkOrigin) {
	    FindingLinkOrigin[FindingLinkOrigin["Direct"] = 100] = "Direct";
	    FindingLinkOrigin[FindingLinkOrigin["Continuity"] = 200] = "Continuity";
	})(FindingLinkOrigin || (enums.FindingLinkOrigin = FindingLinkOrigin = {}));
	enums.FindingLinkOriginSchema = zod_1.z.nativeEnum(FindingLinkOrigin);
	var FindingContinuityDisposition;
	(function (FindingContinuityDisposition) {
	    FindingContinuityDisposition["Reproduced"] = "reproduced";
	    FindingContinuityDisposition["Resolved"] = "resolved";
	})(FindingContinuityDisposition || (enums.FindingContinuityDisposition = FindingContinuityDisposition = {}));
	enums.FindingContinuityDispositionSchema = zod_1.z.nativeEnum(FindingContinuityDisposition);
	var AssertionDefinitionLifecycle;
	(function (AssertionDefinitionLifecycle) {
	    AssertionDefinitionLifecycle[AssertionDefinitionLifecycle["Draft"] = 100] = "Draft";
	    AssertionDefinitionLifecycle[AssertionDefinitionLifecycle["Active"] = 200] = "Active";
	    AssertionDefinitionLifecycle[AssertionDefinitionLifecycle["Retired"] = 300] = "Retired";
	    AssertionDefinitionLifecycle[AssertionDefinitionLifecycle["Deleted"] = 400] = "Deleted";
	})(AssertionDefinitionLifecycle || (enums.AssertionDefinitionLifecycle = AssertionDefinitionLifecycle = {}));
	enums.AssertionDefinitionLifecycleSchema = zod_1.z.nativeEnum(AssertionDefinitionLifecycle);
	
	return enums;
}

var hasRequiredSeverityNames;

function requireSeverityNames () {
	if (hasRequiredSeverityNames) return severityNames;
	hasRequiredSeverityNames = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FRIENDLY_SEVERITY_NAMES = void 0;
		exports.severityFromFriendlyName = severityFromFriendlyName;
		exports.friendlyNameFromSeverity = friendlyNameFromSeverity;
		const enums_1 = requireEnums();
		exports.FRIENDLY_SEVERITY_NAMES = [
		    'not-applicable',
		    'pending',
		    'pass',
		    'recommendation',
		    'indeterminate',
		    'warning',
		    'fail',
		];
		const SEVERITY_BY_FRIENDLY_NAME = {
		    'not-applicable': enums_1.RequirementSeverity.NotApplicable,
		    pending: enums_1.RequirementSeverity.Pending,
		    pass: enums_1.RequirementSeverity.Pass,
		    recommendation: enums_1.RequirementSeverity.Recommendation,
		    indeterminate: enums_1.RequirementSeverity.Indeterminate,
		    warning: enums_1.RequirementSeverity.Warning,
		    fail: enums_1.RequirementSeverity.Fail,
		};
		const FRIENDLY_NAME_BY_SEVERITY = new Map(exports.FRIENDLY_SEVERITY_NAMES.map((name) => [SEVERITY_BY_FRIENDLY_NAME[name], name]));
		function severityFromFriendlyName(name) {
		    return SEVERITY_BY_FRIENDLY_NAME[name];
		}
		function friendlyNameFromSeverity(severity) {
		    const name = FRIENDLY_NAME_BY_SEVERITY.get(severity);
		    if (name === undefined) {
		        throw new Error(`No friendly severity name for numeric severity ${severity}`);
		    }
		    return name;
		}
		
	} (severityNames));
	return severityNames;
}

var hasRequiredSeverityReference;

function requireSeverityReference () {
	if (hasRequiredSeverityReference) return SeverityReference;
	hasRequiredSeverityReference = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SeverityMarkdownDirectiveDefinition = void 0;
		exports.formatSeverityDirective = formatSeverityDirective;
		exports.parseSeverityDirectiveValue = parseSeverityDirectiveValue;
		const severity_names_1 = requireSeverityNames();
		const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
		exports.SeverityMarkdownDirectiveDefinition = Object.freeze({
		    allowedAttributes: ['severity'],
		    contentAttributeName: 'severity',
		    directive: 'severity',
		    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
		});
		function formatSeverityDirective(severity) {
		    return `:${exports.SeverityMarkdownDirectiveDefinition.directive}[${(0, severity_names_1.friendlyNameFromSeverity)(severity)}]`;
		}
		function parseSeverityDirectiveValue(value) {
		    const normalized = value.trim().toLowerCase();
		    const name = severity_names_1.FRIENDLY_SEVERITY_NAMES.find((candidate) => candidate === normalized);
		    return name === undefined ? undefined : (0, severity_names_1.severityFromFriendlyName)(name);
		}
		
	} (SeverityReference));
	return SeverityReference;
}

var SignalReference = {};

var hasRequiredSignalReference;

function requireSignalReference () {
	if (hasRequiredSignalReference) return SignalReference;
	hasRequiredSignalReference = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SignalReferenceMarkdownDirectiveDefinition = void 0;
		exports.formatSignalReferenceDirective = formatSignalReferenceDirective;
		const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
		exports.SignalReferenceMarkdownDirectiveDefinition = Object.freeze({
		    allowedAttributes: ['signalName'],
		    contentAttributeName: 'signalName',
		    directive: 'signal',
		    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
		});
		function formatSignalReferenceDirective(signalName) {
		    return `:${exports.SignalReferenceMarkdownDirectiveDefinition.directive}[${(0, MarkdownDirectiveDefinition_1.escapeDirectiveContent)(signalName)}]`;
		}
		
	} (SignalReference));
	return SignalReference;
}

var SigmaModelSignalPinsTable = {};

var hasRequiredSigmaModelSignalPinsTable;

function requireSigmaModelSignalPinsTable () {
	if (hasRequiredSigmaModelSignalPinsTable) return SigmaModelSignalPinsTable;
	hasRequiredSigmaModelSignalPinsTable = 1;
	Object.defineProperty(SigmaModelSignalPinsTable, "__esModule", { value: true });
	SigmaModelSignalPinsTable.SigmaModelSignalPinsRowSchema = SigmaModelSignalPinsTable.SIGMA_MODEL_SIGNAL_PINS_TABLE_DIRECTIVE = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	SigmaModelSignalPinsTable.SIGMA_MODEL_SIGNAL_PINS_TABLE_DIRECTIVE = 'sigma-model-signal-pins-table';
	SigmaModelSignalPinsTable.SigmaModelSignalPinsRowSchema = zod_1.z.object({
	    rowKey: zod_1.z
	        .string()
	        .describe('The row key, copied exactly from the "Sigma model signal rows" page context. Never invent one.'),
	    modelSignalName: zod_1.z.string().describe('The sigma model signal name the row key belongs to.'),
	    block: zod_1.z
	        .string()
	        .nullable()
	        .describe('The block or array element label from the page context, or null for a simple signal.'),
	    datasheetName: zod_1.z
	        .string()
	        .nullable()
	        .describe('What the datasheet calls this signal, or null when the datasheet uses the model name.'),
	    pins: zod_1.z.array(zod_1.z.string()).describe('The physical pin designators this signal is on, as printed in the datasheet.'),
	});
	
	return SigmaModelSignalPinsTable;
}

var ToolReference = {};

var hasRequiredToolReference;

function requireToolReference () {
	if (hasRequiredToolReference) return ToolReference;
	hasRequiredToolReference = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ToolReferenceMarkdownDirectiveDefinition = void 0;
		exports.formatToolReferenceDirective = formatToolReferenceDirective;
		const MarkdownDirectiveDefinition_1 = requireMarkdownDirectiveDefinition();
		exports.ToolReferenceMarkdownDirectiveDefinition = Object.freeze({
		    allowedAttributes: ['toolId'],
		    contentAttributeName: 'toolId',
		    directive: 'tool',
		    nodeTypes: [MarkdownDirectiveDefinition_1.NodeTypes.TextDirective],
		});
		function formatToolReferenceDirective(toolId) {
		    return `:${exports.ToolReferenceMarkdownDirectiveDefinition.directive}[${(0, MarkdownDirectiveDefinition_1.escapeDirectiveContent)(toolId)}]`;
		}
		
	} (ToolReference));
	return ToolReference;
}

var hasRequiredMarkdown;

function requireMarkdown () {
	if (hasRequiredMarkdown) return Markdown;
	hasRequiredMarkdown = 1;
	(function (exports) {
		var __createBinding = (Markdown && Markdown.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Markdown && Markdown.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireMarkdownDirectiveDefinition(), exports);
		__exportStar(requireClauseReference(), exports);
		__exportStar(requireModuleReference(), exports);
		__exportStar(requireNetReference(), exports);
		__exportStar(requireParametersAndModesTable(), exports);
		__exportStar(requirePdfCitation(), exports);
		__exportStar(requireSeverityReference(), exports);
		__exportStar(requireSignalReference(), exports);
		__exportStar(requireSigmaModelSignalPinsTable(), exports);
		__exportStar(requireToolReference(), exports);
		
	} (Markdown));
	return Markdown;
}

var Mutex = {};

var cadstromMemoryMutex_factory = {};

var cadstromMutexBase = {};

var hasRequiredCadstromMutexBase;

function requireCadstromMutexBase () {
	if (hasRequiredCadstromMutexBase) return cadstromMutexBase;
	hasRequiredCadstromMutexBase = 1;
	Object.defineProperty(cadstromMutexBase, "__esModule", { value: true });
	cadstromMutexBase.CadstromMutexBase = void 0;
	class CadstromMutexBase {
	    async runOperation(operation) {
	        await this.acquire();
	        try {
	            return await operation();
	        }
	        finally {
	            await this.release();
	        }
	    }
	    async [Symbol.asyncDispose]() {
	        await this.release();
	    }
	}
	cadstromMutexBase.CadstromMutexBase = CadstromMutexBase;
	
	return cadstromMutexBase;
}

var hasRequiredCadstromMemoryMutex_factory;

function requireCadstromMemoryMutex_factory () {
	if (hasRequiredCadstromMemoryMutex_factory) return cadstromMemoryMutex_factory;
	hasRequiredCadstromMemoryMutex_factory = 1;
	Object.defineProperty(cadstromMemoryMutex_factory, "__esModule", { value: true });
	cadstromMemoryMutex_factory.CadstromMemoryMutex = cadstromMemoryMutex_factory.CadstromMemoryMutexFactory = void 0;
	const cadstrom_mutex_base_1 = requireCadstromMutexBase();
	const Sleep_1 = requireSleep();
	const Utils_1 = requireUtils$4();
	class CadstromMemoryMutexFactory {
	    constructor(storage = {}) {
	        this.state = storage.state ?? new Map();
	        this.owners = storage.owners ?? [];
	    }
	    createMutex(id, opts) {
	        return new CadstromMemoryMutex(this.state, this.owners, id, opts);
	    }
	    async createAndAcquireMutex(id, opts) {
	        const mutex = this.createMutex(id, opts);
	        await mutex.acquire();
	        return mutex;
	    }
	}
	cadstromMemoryMutex_factory.CadstromMemoryMutexFactory = CadstromMemoryMutexFactory;
	class CadstromMemoryMutex extends cadstrom_mutex_base_1.CadstromMutexBase {
	    constructor(state, owners, identifier, options = {}) {
	        super();
	        this.state = state;
	        this.owners = owners;
	        this.identifier = identifier;
	        this.options = options;
	        this.uid = (0, Utils_1.randomAlphaStr)(36);
	        owners.push(this.uid);
	    }
	    get isAcquired() {
	        return this.uid === this.state.get(this.identifier)?.owner;
	    }
	    stopRefresh() {
	    }
	    async acquire() {
	        const acquired = await this.acquireInternal();
	        if (!acquired) {
	            throw new Error('Failed to acquire lock within the timeout period');
	        }
	    }
	    async acquireInternal() {
	        const { lockTimeout = 1000 * 60, retryInterval = 10, acquireAttemptsLimit = this.options.acquireTimeout && this.options.acquireTimeout > 0 ?
	            Number.POSITIVE_INFINITY
	            : 10, acquireTimeout = 0, } = this.options;
	        const acquireDeadline = acquireTimeout ? Date.now() + acquireTimeout : Date.now() + lockTimeout;
	        let attempt = 0;
	        while (++attempt <= acquireAttemptsLimit) {
	            if (Date.now() > acquireDeadline) {
	                return false;
	            }
	            const isLocked = await this.checkIsLocked();
	            if (!isLocked) {
	                this.state.set(this.identifier, {
	                    timestamp: Date.now() + lockTimeout,
	                    owner: this.uid,
	                });
	                return true;
	            }
	            else {
	                await (0, Sleep_1.sleep)(retryInterval);
	            }
	        }
	        return false;
	    }
	    async tryAcquire() {
	        return await this.acquireInternal();
	    }
	    checkIsLocked() {
	        return Promise.resolve(this.syncCheckIsLocked());
	    }
	    async release() {
	        if (this.uid !== this.state.get(this.identifier)?.owner) {
	            return;
	        }
	        this.state.delete(this.identifier);
	    }
	    syncCheckIsLocked() {
	        if (this.state.get(this.identifier)?.owner === this.uid) {
	            return false;
	        }
	        const lock = this.state.get(this.identifier);
	        if (lock?.timestamp && lock.timestamp > Date.now()) {
	            return true;
	        }
	        return false;
	    }
	}
	cadstromMemoryMutex_factory.CadstromMemoryMutex = CadstromMemoryMutex;
	
	return cadstromMemoryMutex_factory;
}

var cadstromMultiMutex = {};

var hasRequiredCadstromMultiMutex;

function requireCadstromMultiMutex () {
	if (hasRequiredCadstromMultiMutex) return cadstromMultiMutex;
	hasRequiredCadstromMultiMutex = 1;
	Object.defineProperty(cadstromMultiMutex, "__esModule", { value: true });
	cadstromMultiMutex.CadstromMultiMutex = void 0;
	const cadstrom_mutex_base_1 = requireCadstromMutexBase();
	class CadstromMultiMutex extends cadstrom_mutex_base_1.CadstromMutexBase {
	    constructor(mutexes, identifier = mutexes.map((m) => m.identifier).join('|')) {
	        super();
	        this.mutexes = mutexes;
	        this.identifier = identifier;
	        if (mutexes.length === 0) {
	            throw new Error('CadstromMultiMutex requires at least one mutex');
	        }
	    }
	    get isAcquired() {
	        return this.mutexes.every((mutex) => mutex.isAcquired);
	    }
	    stopRefresh() {
	        for (const mutex of this.mutexes) {
	            mutex.stopRefresh();
	        }
	    }
	    async acquire() {
	        const acquiredMutexes = [];
	        try {
	            for (const mutex of this.mutexes) {
	                await mutex.acquire();
	                acquiredMutexes.push(mutex);
	            }
	        }
	        catch (error) {
	            await this.releaseSpecificMutexes(acquiredMutexes);
	            throw error;
	        }
	    }
	    async tryAcquire() {
	        const acquiredMutexes = [];
	        try {
	            for (const mutex of this.mutexes) {
	                const acquired = await mutex.tryAcquire();
	                if (!acquired) {
	                    await this.releaseSpecificMutexes(acquiredMutexes);
	                    return false;
	                }
	                acquiredMutexes.push(mutex);
	            }
	            return true;
	        }
	        catch (error) {
	            await this.releaseSpecificMutexes(acquiredMutexes);
	            return false;
	        }
	    }
	    async release() {
	        await this.releaseSpecificMutexes([...this.mutexes].reverse());
	    }
	    async releaseSpecificMutexes(mutexesToRelease) {
	        for (const mutex of mutexesToRelease) {
	            try {
	                await mutex.release();
	            }
	            catch (error) {
	                console.error(`Failed to release mutex ${mutex.identifier}:`, error);
	            }
	        }
	    }
	}
	cadstromMultiMutex.CadstromMultiMutex = CadstromMultiMutex;
	
	return cadstromMultiMutex;
}

var lockManager = {};

var hasRequiredLockManager;

function requireLockManager () {
	if (hasRequiredLockManager) return lockManager;
	hasRequiredLockManager = 1;
	Object.defineProperty(lockManager, "__esModule", { value: true });
	
	return lockManager;
}

var lockOptions = {};

var hasRequiredLockOptions;

function requireLockOptions () {
	if (hasRequiredLockOptions) return lockOptions;
	hasRequiredLockOptions = 1;
	Object.defineProperty(lockOptions, "__esModule", { value: true });
	
	return lockOptions;
}

var memoryLockManager = {};

var hasRequiredMemoryLockManager;

function requireMemoryLockManager () {
	if (hasRequiredMemoryLockManager) return memoryLockManager;
	hasRequiredMemoryLockManager = 1;
	Object.defineProperty(memoryLockManager, "__esModule", { value: true });
	memoryLockManager.MemoryLockManager = void 0;
	class MemoryLockManager {
	    constructor() {
	        this.locks = new Map();
	        this.nextUid = 0;
	    }
	    async request(name, optionsOrCallback, maybeCallback) {
	        const { options, callback } = this.parseArgs(optionsOrCallback, maybeCallback);
	        const mode = options.mode ?? 'exclusive';
	        const ifAvailable = options.ifAvailable ?? false;
	        const signal = options.signal;
	        const state = this.getOrCreateState(name);
	        const uid = String(++this.nextUid);
	        if (ifAvailable) {
	            if (!this.canAcquire(state, mode)) {
	                return callback(null);
	            }
	            this.addHolder(state, uid, mode);
	            const lock = { name, mode };
	            try {
	                return await callback(lock);
	            }
	            finally {
	                this.removeHolder(state, uid, mode);
	                this.drain(state);
	                this.cleanupState(name, state);
	            }
	        }
	        if (this.canAcquire(state, mode)) {
	            this.addHolder(state, uid, mode);
	        }
	        else {
	            await this.enqueue(state, uid, mode, signal);
	        }
	        const lock = { name, mode };
	        try {
	            return await callback(lock);
	        }
	        finally {
	            this.removeHolder(state, uid, mode);
	            this.drain(state);
	            this.cleanupState(name, state);
	        }
	    }
	    async query() {
	        const held = [];
	        const pending = [];
	        for (const [name, state] of this.locks) {
	            if (state.writer !== null) {
	                held.push({ name, mode: 'exclusive' });
	            }
	            for (const _reader of state.readers) {
	                held.push({ name, mode: 'shared' });
	            }
	            for (const waiter of state.waitQueue) {
	                pending.push({ name, mode: waiter.mode });
	            }
	        }
	        return { held, pending };
	    }
	    canAcquire(state, mode) {
	        if (mode === 'shared') {
	            if (state.writer !== null) {
	                return false;
	            }
	            const hasExclusiveWaiter = state.waitQueue.some((w) => w.mode === 'exclusive');
	            if (hasExclusiveWaiter) {
	                return false;
	            }
	            return true;
	        }
	        return state.readers.size === 0 && state.writer === null;
	    }
	    addHolder(state, uid, mode) {
	        if (mode === 'shared') {
	            state.readers.add(uid);
	        }
	        else {
	            state.writer = uid;
	        }
	    }
	    removeHolder(state, uid, mode) {
	        if (mode === 'shared') {
	            state.readers.delete(uid);
	        }
	        else if (state.writer === uid) {
	            state.writer = null;
	        }
	    }
	    enqueue(state, uid, mode, signal) {
	        if (signal?.aborted) {
	            return Promise.reject(signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'));
	        }
	        return new Promise((resolve, reject) => {
	            const waiter = { uid, mode, resolve, reject };
	            state.waitQueue.push(waiter);
	            if (signal) {
	                const onAbort = () => {
	                    const idx = state.waitQueue.indexOf(waiter);
	                    if (idx !== -1) {
	                        state.waitQueue.splice(idx, 1);
	                        reject(signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'));
	                    }
	                };
	                signal.addEventListener('abort', onAbort, { once: true });
	                const originalResolve = waiter.resolve;
	                const patchedWaiter = waiter;
	                patchedWaiter.resolve = () => {
	                    signal.removeEventListener('abort', onAbort);
	                    originalResolve();
	                };
	            }
	        });
	    }
	    drain(state) {
	        while (state.waitQueue.length > 0) {
	            const next = state.waitQueue[0];
	            if (!this.canAcquireForDrain(state, next.mode)) {
	                break;
	            }
	            state.waitQueue.shift();
	            this.addHolder(state, next.uid, next.mode);
	            next.resolve();
	            if (next.mode === 'exclusive') {
	                break;
	            }
	        }
	    }
	    canAcquireForDrain(state, mode) {
	        if (mode === 'shared') {
	            return state.writer === null;
	        }
	        return state.readers.size === 0 && state.writer === null;
	    }
	    getOrCreateState(name) {
	        let state = this.locks.get(name);
	        if (!state) {
	            state = { readers: new Set(), writer: null, waitQueue: [] };
	            this.locks.set(name, state);
	        }
	        return state;
	    }
	    cleanupState(name, state) {
	        if (state.readers.size === 0 && state.writer === null && state.waitQueue.length === 0) {
	            this.locks.delete(name);
	        }
	    }
	    parseArgs(optionsOrCallback, maybeCallback) {
	        if (typeof optionsOrCallback === 'function') {
	            return { options: {}, callback: optionsOrCallback };
	        }
	        return { options: optionsOrCallback, callback: maybeCallback };
	    }
	}
	memoryLockManager.MemoryLockManager = MemoryLockManager;
	
	return memoryLockManager;
}

var mutex = {};

var hasRequiredMutex$1;

function requireMutex$1 () {
	if (hasRequiredMutex$1) return mutex;
	hasRequiredMutex$1 = 1;
	Object.defineProperty(mutex, "__esModule", { value: true });
	
	return mutex;
}

var mutexFactory = {};

var hasRequiredMutexFactory;

function requireMutexFactory () {
	if (hasRequiredMutexFactory) return mutexFactory;
	hasRequiredMutexFactory = 1;
	Object.defineProperty(mutexFactory, "__esModule", { value: true });
	
	return mutexFactory;
}

var hasRequiredMutex;

function requireMutex () {
	if (hasRequiredMutex) return Mutex;
	hasRequiredMutex = 1;
	(function (exports) {
		var __createBinding = (Mutex && Mutex.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Mutex && Mutex.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireCadstromMemoryMutex_factory(), exports);
		__exportStar(requireCadstromMultiMutex(), exports);
		__exportStar(requireCadstromMutexBase(), exports);
		__exportStar(requireLockManager(), exports);
		__exportStar(requireLockOptions(), exports);
		__exportStar(requireMemoryLockManager(), exports);
		__exportStar(requireMutex$1(), exports);
		__exportStar(requireMutexFactory(), exports);
		
	} (Mutex));
	return Mutex;
}

var RequirementsV2$1 = {};

var canonicalDefinitions = {};

var componentFunction = {};

var hasRequiredComponentFunction;

function requireComponentFunction () {
	if (hasRequiredComponentFunction) return componentFunction;
	hasRequiredComponentFunction = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentFunctionAssertions = exports.FunctionExternalConnectionsAssigned = exports.FunctionExternalComponentsAssigned = exports.FunctionDependenciesSatisfied = exports.FunctionSignalAssignmentsValid = exports.FunctionRequiredSignalsAssigned = exports.RequiredFunctionsUsed = exports.ComponentFunctionRequirement = void 0;
		const enums_1 = requireEnums();
		const COMPONENT_FUNCTION_REQUIREMENT_ID = 'req_cadstromcfun000000000000000000000000';
		exports.ComponentFunctionRequirement = {
		    id: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    slug: 'cadstrom.function',
		    slugPrefix: 'FUNCTION',
		    name: 'Component Function',
		    description: 'Asserts that every function a component’s package declares — an electrical role the part can perform (an I²C controller, a voltage regulator, a GPIO) — is correctly and completely implemented: mandatory functions are used, a used function assigns all of its required signals with valid assignments, its dependencies are satisfied, and its required external components and connections are present. Evaluated once per component function.',
		};
		exports.RequiredFunctionsUsed = {
		    id: 'asrt_cfunreqused000000000000000000000000',
		    slug: 'cadstrom.function.usage.required_functions_used',
		    requirementDefinitionId: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    name: 'Required component functions must be used',
		    description: 'A function the component’s package declares as mandatory must be assigned to an active role in the design. A required function left unused means a capability the part must provide is absent.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.FunctionRequiredSignalsAssigned = {
		    id: 'asrt_cfunsigassigned00000000000000000000',
		    slug: 'cadstrom.function.signals.required_signals_assigned',
		    requirementDefinitionId: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    name: 'Every required function signal must be assigned',
		    description: 'Every required (non-optional) signal of a used function — including the full set of co-required signals for multi-signal functions — must be assigned to a component signal. A function with an unassigned required signal is incompletely implemented.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.FunctionSignalAssignmentsValid = {
		    id: 'asrt_cfunassignvalid00000000000000000000',
		    slug: 'cadstrom.function.signals.assignments_valid',
		    requirementDefinitionId: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    name: 'Function signal assignments must be valid',
		    description: 'Every assigned component signal must be a legal target for its function signal; a signal that does not permit multiple assignments must have exactly one, and no signal may exceed its maximum number of assignments.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.FunctionDependenciesSatisfied = {
		    id: 'asrt_cfundepssatisfied000000000000000000',
		    slug: 'cadstrom.function.dependencies.satisfied',
		    requirementDefinitionId: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    name: 'Function dependencies must be satisfied',
		    description: 'The conditions for the function to be used must be met: any other functions it depends on must themselves be in the required used/unused state, and any device mode it requires must be in the necessary state.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.FunctionExternalComponentsAssigned = {
		    id: 'asrt_cfunextcomponents000000000000000000',
		    slug: 'cadstrom.function.external.components_assigned',
		    requirementDefinitionId: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    name: 'Required external components must be present and correctly assigned',
		    description: 'Every external component the function requires (declared via its conditions) — a bus counterpart, a required bias resistor — must be assigned to a valid component reachable from the function’s signals.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.FunctionExternalConnectionsAssigned = {
		    id: 'asrt_cfunextconnections00000000000000000',
		    slug: 'cadstrom.function.external.connections_assigned',
		    requirementDefinitionId: COMPONENT_FUNCTION_REQUIREMENT_ID,
		    name: 'Required external connections must be assigned',
		    description: 'Every external connection the function’s required components declare must be assigned. An unassigned connection leaves a required external hookup absent.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ComponentFunctionAssertions = [
		    exports.RequiredFunctionsUsed,
		    exports.FunctionRequiredSignalsAssigned,
		    exports.FunctionSignalAssignmentsValid,
		    exports.FunctionDependenciesSatisfied,
		    exports.FunctionExternalComponentsAssigned,
		    exports.FunctionExternalConnectionsAssigned,
		];
		
	} (componentFunction));
	return componentFunction;
}

var componentLimits = {};

var hasRequiredComponentLimits;

function requireComponentLimits () {
	if (hasRequiredComponentLimits) return componentLimits;
	hasRequiredComponentLimits = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentLimitsAssertions = exports.LimitsComponentValidated = exports.LimitsCapacitanceEqualsRequired = exports.LimitsCapacitanceMinMet = exports.LimitsCapacitanceMaxNotExceeded = exports.LimitsInductanceEqualsRequired = exports.LimitsInductanceMinMet = exports.LimitsInductanceMaxNotExceeded = exports.LimitsResistanceEqualsRequired = exports.LimitsResistanceMinMet = exports.LimitsResistanceMaxNotExceeded = exports.LimitsFrequencyEqualsRequired = exports.LimitsFrequencyMinMet = exports.LimitsFrequencyMaxNotExceeded = exports.LimitsTemperatureEqualsRequired = exports.LimitsTemperatureMinMet = exports.LimitsTemperatureMaxNotExceeded = exports.LimitsPowerEqualsRequired = exports.LimitsPowerMinMet = exports.LimitsPowerMaxNotExceeded = exports.LimitsCurrentEqualsRequired = exports.LimitsCurrentMinMet = exports.LimitsCurrentMaxNotExceeded = exports.LimitsVoltageEqualsRequired = exports.LimitsVoltageMinMet = exports.LimitsVoltageMaxNotExceeded = exports.ComponentLimitsRequirement = void 0;
		const enums_1 = requireEnums();
		const COMPONENT_LIMITS_REQUIREMENT_ID = 'req_cadstromlimits0000000000000000000000';
		exports.ComponentLimitsRequirement = {
		    id: COMPONENT_LIMITS_REQUIREMENT_ID,
		    slug: 'cadstrom.limits',
		    slugPrefix: 'LIMS',
		    name: 'Component Limits',
		    description: 'Asserts that every signal of a component operates within its manufacturer-declared electrical limits, checked against the resolved worst-case states of its connected nets. Evaluated on a per-pin basis.\n\n- **Absolute maximum ratings (AMR)** — limits beyond which the part risks damage; violations are reported as Errors.\n- **Recommended operating conditions (OC)** — the range within which the part is specified to function; violations are reported as Warnings.',
		};
		exports.LimitsVoltageMaxNotExceeded = {
		    id: 'asrt_limitsvoltagemax00000000000000000000',
		    slug: 'cadstrom.limits.voltage.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Voltage must not exceed its maximum limit',
		    description: 'The worst-case voltage at a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsVoltageMinMet = {
		    id: 'asrt_limitsvoltagemin00000000000000000000',
		    slug: 'cadstrom.limits.voltage.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Voltage must not fall below its minimum limit',
		    description: 'The worst-case voltage at a component signal must not fall below its declared minimum.\n\n- Falling below an **absolute minimum rating (AMR)** (commonly a negative limit guarding against undershoot) risks damage to the part and is reported as an Error.\n- Falling below a **recommended operating minimum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsVoltageEqualsRequired = {
		    id: 'asrt_limitsvoltageeq000000000000000000000',
		    slug: 'cadstrom.limits.voltage.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Voltage must equal its required value',
		    description: 'A component signal whose voltage is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsCurrentMaxNotExceeded = {
		    id: 'asrt_limitscurrentmax00000000000000000000',
		    slug: 'cadstrom.limits.current.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Current must not exceed its maximum limit',
		    description: 'The worst-case current through a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** (e.g. a maximum sink or source current) risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsCurrentMinMet = {
		    id: 'asrt_limitscurrentmin00000000000000000000',
		    slug: 'cadstrom.limits.current.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Current must not fall below its minimum limit',
		    description: 'The worst-case current through a component signal must not fall below its declared minimum (e.g. a regulator minimum load current).\n\n- Falling below an **absolute minimum rating (AMR)** risks damage or undefined operation and is reported as an Error.\n- Falling below a **recommended operating minimum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsCurrentEqualsRequired = {
		    id: 'asrt_limitscurrenteq000000000000000000000',
		    slug: 'cadstrom.limits.current.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Current must equal its required value',
		    description: 'A component signal whose current is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsPowerMaxNotExceeded = {
		    id: 'asrt_limitspowermax0000000000000000000000',
		    slug: 'cadstrom.limits.power.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Power dissipation must not exceed its maximum limit',
		    description: 'The worst-case power dissipated at a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** (e.g. a resistor power rating) risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsPowerMinMet = {
		    id: 'asrt_limitspowermin0000000000000000000000',
		    slug: 'cadstrom.limits.power.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Power dissipation must not fall below its minimum limit',
		    description: 'The worst-case power dissipated at a component signal must not fall below its declared minimum. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsPowerEqualsRequired = {
		    id: 'asrt_limitspowereq00000000000000000000000',
		    slug: 'cadstrom.limits.power.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Power dissipation must equal its required value',
		    description: 'A component signal whose power dissipation is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsTemperatureMaxNotExceeded = {
		    id: 'asrt_limitstempmax00000000000000000000000',
		    slug: 'cadstrom.limits.temperature.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Temperature must not exceed its maximum limit',
		    description: 'The worst-case temperature at a component signal must not exceed its declared maximum, regardless of the unit the limit is declared in.\n\n- Exceeding an **absolute maximum rating (AMR)** (e.g. a maximum junction temperature) risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsTemperatureMinMet = {
		    id: 'asrt_limitstempmin00000000000000000000000',
		    slug: 'cadstrom.limits.temperature.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Temperature must not fall below its minimum limit',
		    description: 'The worst-case temperature at a component signal must not fall below its declared minimum, regardless of the unit the limit is declared in. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsTemperatureEqualsRequired = {
		    id: 'asrt_limitstempeq000000000000000000000000',
		    slug: 'cadstrom.limits.temperature.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Temperature must equal its required value',
		    description: 'A component signal whose temperature is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsFrequencyMaxNotExceeded = {
		    id: 'asrt_limitsfreqmax00000000000000000000000',
		    slug: 'cadstrom.limits.frequency.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Frequency must not exceed its maximum limit',
		    description: 'The worst-case frequency at a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsFrequencyMinMet = {
		    id: 'asrt_limitsfreqmin00000000000000000000000',
		    slug: 'cadstrom.limits.frequency.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Frequency must not fall below its minimum limit',
		    description: 'The worst-case frequency at a component signal must not fall below its declared minimum. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsFrequencyEqualsRequired = {
		    id: 'asrt_limitsfreqeq000000000000000000000000',
		    slug: 'cadstrom.limits.frequency.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Frequency must equal its required value',
		    description: 'A component signal whose frequency is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsResistanceMaxNotExceeded = {
		    id: 'asrt_limitsresmax000000000000000000000000',
		    slug: 'cadstrom.limits.resistance.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Resistance must not exceed its maximum limit',
		    description: 'The worst-case resistance at a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsResistanceMinMet = {
		    id: 'asrt_limitsresmin000000000000000000000000',
		    slug: 'cadstrom.limits.resistance.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Resistance must not fall below its minimum limit',
		    description: 'The worst-case resistance at a component signal must not fall below its declared minimum. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsResistanceEqualsRequired = {
		    id: 'asrt_limitsreseq0000000000000000000000000',
		    slug: 'cadstrom.limits.resistance.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Resistance must equal its required value',
		    description: 'A component signal whose resistance is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsInductanceMaxNotExceeded = {
		    id: 'asrt_limitsindmax000000000000000000000000',
		    slug: 'cadstrom.limits.inductance.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Inductance must not exceed its maximum limit',
		    description: 'The worst-case inductance at a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsInductanceMinMet = {
		    id: 'asrt_limitsindmin000000000000000000000000',
		    slug: 'cadstrom.limits.inductance.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Inductance must not fall below its minimum limit',
		    description: 'The worst-case inductance at a component signal must not fall below its declared minimum. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsInductanceEqualsRequired = {
		    id: 'asrt_limitsindeq0000000000000000000000000',
		    slug: 'cadstrom.limits.inductance.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Inductance must equal its required value',
		    description: 'A component signal whose inductance is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsCapacitanceMaxNotExceeded = {
		    id: 'asrt_limitscapmax000000000000000000000000',
		    slug: 'cadstrom.limits.capacitance.max_not_exceeded',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Capacitance must not exceed its maximum limit',
		    description: 'The worst-case capacitance at a component signal must not exceed its declared maximum.\n\n- Exceeding an **absolute maximum rating (AMR)** risks damage to the part and is reported as an Error.\n- Exceeding a **recommended operating maximum (OC)** leaves the range within which the part is specified to function and is reported as a Warning.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsCapacitanceMinMet = {
		    id: 'asrt_limitscapmin000000000000000000000000',
		    slug: 'cadstrom.limits.capacitance.min_met',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Capacitance must not fall below its minimum limit',
		    description: 'The worst-case capacitance at a component signal must not fall below its declared minimum. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsCapacitanceEqualsRequired = {
		    id: 'asrt_limitscapeq0000000000000000000000000',
		    slug: 'cadstrom.limits.capacitance.equals_required',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Capacitance must equal its required value',
		    description: 'A component signal whose capacitance is required to be a fixed value (optionally with a tolerance) must resolve to that value. AMR violations are reported as Errors; OC violations are reported as Warnings.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LimitsComponentValidated = {
		    id: 'asrt_limitscomponentval000000000000000000',
		    slug: 'cadstrom.limits.component.limits_validated',
		    requirementDefinitionId: COMPONENT_LIMITS_REQUIREMENT_ID,
		    name: 'Component limits must be validated',
		    description: 'A component must declare electrical limits, and the analysis must be able to check them against the resolved worst-case states of its connected nets. Evaluated once per component; resolves Indeterminate when the component has no usable limits data or when one or more of its net states could not be resolved. A violation that cannot be attributed to a specific signal is carried here.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ComponentLimitsAssertions = [
		    exports.LimitsVoltageMaxNotExceeded,
		    exports.LimitsVoltageMinMet,
		    exports.LimitsVoltageEqualsRequired,
		    exports.LimitsCurrentMaxNotExceeded,
		    exports.LimitsCurrentMinMet,
		    exports.LimitsCurrentEqualsRequired,
		    exports.LimitsPowerMaxNotExceeded,
		    exports.LimitsPowerMinMet,
		    exports.LimitsPowerEqualsRequired,
		    exports.LimitsTemperatureMaxNotExceeded,
		    exports.LimitsTemperatureMinMet,
		    exports.LimitsTemperatureEqualsRequired,
		    exports.LimitsFrequencyMaxNotExceeded,
		    exports.LimitsFrequencyMinMet,
		    exports.LimitsFrequencyEqualsRequired,
		    exports.LimitsResistanceMaxNotExceeded,
		    exports.LimitsResistanceMinMet,
		    exports.LimitsResistanceEqualsRequired,
		    exports.LimitsInductanceMaxNotExceeded,
		    exports.LimitsInductanceMinMet,
		    exports.LimitsInductanceEqualsRequired,
		    exports.LimitsCapacitanceMaxNotExceeded,
		    exports.LimitsCapacitanceMinMet,
		    exports.LimitsCapacitanceEqualsRequired,
		    exports.LimitsComponentValidated,
		];
		
	} (componentLimits));
	return componentLimits;
}

var componentResource = {};

var hasRequiredComponentResource;

function requireComponentResource () {
	if (hasRequiredComponentResource) return componentResource;
	hasRequiredComponentResource = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentResourceAssertions = exports.ResourceConsumptionConditionsMet = exports.MandatoryResourcesConsumed = exports.ComponentResourceRequirement = void 0;
		const enums_1 = requireEnums();
		const COMPONENT_RESOURCE_REQUIREMENT_ID = 'req_cadstromcres000000000000000000000000';
		exports.ComponentResourceRequirement = {
		    id: COMPONENT_RESOURCE_REQUIREMENT_ID,
		    slug: 'cadstrom.resource',
		    slugPrefix: 'RESOURCE',
		    name: 'Component Resource',
		    description: 'Asserts that every resource a component’s package declares — an internal capability only one function may use at a time (a communication core, a configurable block) — is consumed as required: a resource marked mandatory is consumed by a used function, and a consumed resource’s conditions are met. Evaluated once per component resource.',
		};
		exports.MandatoryResourcesConsumed = {
		    id: 'asrt_cresmustconsumed0000000000000000000',
		    slug: 'cadstrom.resource.consumption.mandatory_consumed',
		    requirementDefinitionId: COMPONENT_RESOURCE_REQUIREMENT_ID,
		    name: 'A resource that must be consumed must be consumed by a function',
		    description: 'A resource the package marks as mandatory must be consumed by a used function in the evaluated operating state. A mandatory resource left unconsumed means the component has no function driving that capability.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ResourceConsumptionConditionsMet = {
		    id: 'asrt_cresconditionsmet000000000000000000',
		    slug: 'cadstrom.resource.consumption.conditions_met',
		    requirementDefinitionId: COMPONENT_RESOURCE_REQUIREMENT_ID,
		    name: 'A consumed resource’s conditions must be met',
		    description: 'When a function consumes a resource, the conditions the resource declares (its required device modes) must hold in the evaluated operating state.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Draft,
		};
		exports.ComponentResourceAssertions = [
		    exports.MandatoryResourcesConsumed,
		    exports.ResourceConsumptionConditionsMet,
		];
		
	} (componentResource));
	return componentResource;
}

var componentValues = {};

var hasRequiredComponentValues;

function requireComponentValues () {
	if (hasRequiredComponentValues) return componentValues;
	hasRequiredComponentValues = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentValuesAssertions = exports.ComponentValueValidated = exports.ComponentValuesRequirement = void 0;
		const enums_1 = requireEnums();
		const COMPONENT_VALUES_REQUIREMENT_ID = 'req_cadstromcval000000000000000000000000';
		exports.ComponentValuesRequirement = {
		    id: COMPONENT_VALUES_REQUIREMENT_ID,
		    slug: 'cadstrom.component_value',
		    slugPrefix: 'CVAL',
		    name: 'Component Values',
		    description: 'Asserts that a resistor, capacitor, or inductor value from the design can be compared to the resolved package value, and that the comparable values match.',
		};
		exports.ComponentValueValidated = {
		    id: 'asrt_cvalvalidated00000000000000000000000',
		    slug: 'cadstrom.component_value.validated',
		    requirementDefinitionId: COMPONENT_VALUES_REQUIREMENT_ID,
		    name: 'Component value must match the resolved package value',
		    description: 'The design value and resolved package value must both be readable, and the comparable resistor, capacitor, or inductor value must match the resolved package value.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ComponentValuesAssertions = [exports.ComponentValueValidated];
		
	} (componentValues));
	return componentValues;
}

var descriptor_types = {};

var hasRequiredDescriptor_types;

function requireDescriptor_types () {
	if (hasRequiredDescriptor_types) return descriptor_types;
	hasRequiredDescriptor_types = 1;
	Object.defineProperty(descriptor_types, "__esModule", { value: true });
	
	return descriptor_types;
}

var differentialPair = {};

var hasRequiredDifferentialPair;

function requireDifferentialPair () {
	if (hasRequiredDifferentialPair) return differentialPair;
	hasRequiredDifferentialPair = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DifferentialPairAssertions = exports.DifferentialPairLegsSymmetric = exports.DifferentialPairEndpointConnected = exports.DifferentialPairRequirement = void 0;
		const enums_1 = requireEnums();
		const DIFFERENTIAL_PAIR_REQUIREMENT_ID = 'req_cadstromdiffpair00000000000000000000';
		exports.DifferentialPairRequirement = {
		    id: DIFFERENTIAL_PAIR_REQUIREMENT_ID,
		    slug: 'cadstrom.diff_pair',
		    slugPrefix: 'DIFF',
		    name: 'Differential Pairs',
		    description: 'Asserts that every interface differential pair endpoint connects to its partner with matching polarity, and that the two legs of each pair carry matching series parts.',
		};
		exports.DifferentialPairEndpointConnected = {
		    id: 'asrt_diffpairendpointconnected00000000000',
		    slug: 'cadstrom.diff_pair.endpoint_connected',
		    requirementDefinitionId: DIFFERENTIAL_PAIR_REQUIREMENT_ID,
		    name: 'Differential pair endpoint must connect to its partner with matching polarity',
		    description: 'Both pins of a differential pair endpoint must connect, positive to positive and negative to negative, to the same partner endpoint, either directly or through one coupling capacitor, ferrite, or low-value resistor on each leg. A swapped, half-connected, or split pair corrupts the signal; an endpoint whose pins are both unconnected is unused and not applicable.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.DifferentialPairLegsSymmetric = {
		    id: 'asrt_diffpairlegssymmetric000000000000000',
		    slug: 'cadstrom.diff_pair.legs_symmetric',
		    requirementDefinitionId: DIFFERENTIAL_PAIR_REQUIREMENT_ID,
		    name: 'Differential pair legs must carry matching series parts',
		    description: 'Where a differential pair passes through series parts such as AC-coupling capacitors, ferrites, or series resistors, both legs must carry the same kind of part with the same value. A part on one leg only, or a mismatched value, unbalances the pair and converts differential signal into common-mode noise.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.DifferentialPairAssertions = [
		    exports.DifferentialPairEndpointConnected,
		    exports.DifferentialPairLegsSymmetric,
		];
		
	} (differentialPair));
	return differentialPair;
}

var electricalInterfaceCompatibility = {};

var hasRequiredElectricalInterfaceCompatibility;

function requireElectricalInterfaceCompatibility () {
	if (hasRequiredElectricalInterfaceCompatibility) return electricalInterfaceCompatibility;
	hasRequiredElectricalInterfaceCompatibility = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ElectricalInterfaceCompatibilityAssertions = exports.PinOffBoardConnection = exports.PinExternalConnectionsMatchSpec = exports.PinFloatMatchesSpec = exports.OutputSingleSidedDriverBiased = exports.OutputNoConflictingDriver = exports.InputAnalogDrivenValid = exports.InputReachesFunctionalState = exports.InputNoIndeterminateState = exports.InputVilMet = exports.InputVihMet = exports.ElectricalInterfaceCompatibilityRequirement = void 0;
		const enums_1 = requireEnums();
		const ELECTRICAL_INTERFACE_REQUIREMENT_ID = 'req_cadstromeif0000000000000000000000000';
		exports.ElectricalInterfaceCompatibilityRequirement = {
		    id: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    slug: 'cadstrom.eif',
		    slugPrefix: 'EIF',
		    name: 'Electrical Interface Compatibility',
		    description: 'Asserts that every pin presents a compatible electrical interface to whatever it connects to — each driven input reaches a valid level with robust margin against device min/max, no net is left contended or indeterminate, single-sided drivers are biased to their passive level, no pin is improperly floating, and off-board pins are compatible with the external interface they mate. Evaluated on a pin-by-pin basis.',
		};
		exports.InputVihMet = {
		    id: 'asrt_eifinpvihmet000000000000000000000000',
		    slug: 'cadstrom.eif.input.vih_met',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Digital inputs must be driven such that VIH is met',
		    description: 'A signal that effectively drives a digital input must meet that input’s VIH, whether driven dynamically (e.g. by a GPO) or statically (e.g. biased high through a resistor). Resolved Not Applicable for inputs statically biased low.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.InputVilMet = {
		    id: 'asrt_eifinpvilmet000000000000000000000000',
		    slug: 'cadstrom.eif.input.vil_met',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Digital inputs must be driven such that VIL is met',
		    description: 'A signal that effectively drives a digital input must meet that input’s VIL, whether driven dynamically or statically (e.g. biased low through a resistor). Resolved Not Applicable for inputs statically biased high.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.InputNoIndeterminateState = {
		    id: 'asrt_eifinpnoindet00000000000000000000000',
		    slug: 'cadstrom.eif.input.no_indeterminate_state',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Digital inputs must not be left in unpermitted indeterminate states',
		    description: 'A digital input must never be able to settle to a voltage between its VIL and VIH (the indeterminate region) unless indeterminate operation has been explicitly permitted for that input.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.InputReachesFunctionalState = {
		    id: 'asrt_eifinpreachfn00000000000000000000000',
		    slug: 'cadstrom.eif.input.reaches_functional_state',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Digital inputs must be able to reach their nominally functional state',
		    description: 'Given the drivers and bias network present on its net, a digital input must be able to attain the logic state required for the component to function nominally. Resolved Not Applicable for inputs with no defined nominally functional state.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.InputAnalogDrivenValid = {
		    id: 'asrt_eifinpanalogdrv000000000000000000000',
		    slug: 'cadstrom.eif.input.analog_driven_valid',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Digital inputs driven by analog outputs must be functionally valid',
		    description: 'Where an analog output drives a digital input, the analog signal must be able to resolve to valid logic levels at that input — its swing must reach both a valid logic HIGH (≥ VIH) and LOW (≤ VIL). A digital output driving an analog input is not evaluated here.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.OutputNoConflictingDriver = {
		    id: 'asrt_eifoutnoconflict00000000000000000000',
		    slug: 'cadstrom.eif.output.no_conflicting_driver',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Digital outputs must not have a conflicting driver',
		    description: 'A net driven by a digital output must not be simultaneously driven by another output to a conflicting logic level (e.g. two push-pull outputs, or a mix of high- and low-side single-sided drivers).',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.OutputSingleSidedDriverBiased = {
		    id: 'asrt_eifoutsinglesided0000000000000000000',
		    slug: 'cadstrom.eif.output.single_sided_driver_biased',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Single-sided drivers must be correctly biased',
		    description: 'A single-sided driver — one that can actively drive only one logic level — must have the bias network needed to reach its passive level: an open-drain output needs a pull-up to a known supply meeting receiver VIH; an open-source output needs a pull-down meeting receiver VIL.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PinFloatMatchesSpec = {
		    id: 'asrt_eifpinfloatspec000000000000000000000',
		    slug: 'cadstrom.eif.pin.float_matches_spec',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Pin floating behavior must match specification',
		    description: 'A pin’s floating state must match its specification in both directions. A pin must not be left floating — connected to no driver and no bias source — unless floating is permitted for it (e.g. a documented no-connect); conversely, a pin that is required to float — e.g. a reserved or do-not-connect pin — must have no driver, bias, or connection to any other pin, since driving or biasing it can violate the device’s requirements. Pins with internal bias resistors are generally not considered floating.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PinExternalConnectionsMatchSpec = {
		    id: 'asrt_eifpinextconnspec0000000000000000000',
		    slug: 'cadstrom.eif.pin.external_connections_match_spec',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Pin external connections must match specification',
		    description: 'Every external connection specified for a pin must be present on the design. A connection is either direct — the pin strapped to another pin or rail, e.g. EN to VIN — or through a component such as a pull-up or pull-down resistor or a decoupling capacitor; the component must be of the specified kind, fitted, and within the specified value. A pin with several specified connections is judged on all of them together; a missing or incorrect connection leaves the pin without the bias, decoupling, or configuration the device requires.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PinOffBoardConnection = {
		    id: 'asrt_eifpinoffboard0000000000000000000000',
		    slug: 'cadstrom.eif.pin.off_board_connection',
		    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
		    name: 'Pins connected to external connectors must be functionally compatible with the external system',
		    description: 'A pin leaving the board through a connector must be functionally compatible with the external system it mates with. Compatibility can only be confirmed for known connector types whose interface is defined (e.g. USB); otherwise the external interface should be verified by hand.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ElectricalInterfaceCompatibilityAssertions = [
		    exports.InputVihMet,
		    exports.InputVilMet,
		    exports.InputNoIndeterminateState,
		    exports.InputReachesFunctionalState,
		    exports.InputAnalogDrivenValid,
		    exports.OutputNoConflictingDriver,
		    exports.OutputSingleSidedDriverBiased,
		    exports.PinFloatMatchesSpec,
		    exports.PinExternalConnectionsMatchSpec,
		    exports.PinOffBoardConnection,
		];
		
	} (electricalInterfaceCompatibility));
	return electricalInterfaceCompatibility;
}

var implementationRules$1 = {};

var hasRequiredImplementationRules$2;

function requireImplementationRules$2 () {
	if (hasRequiredImplementationRules$2) return implementationRules$1;
	hasRequiredImplementationRules$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ImplementationRulesRequirement = void 0;
		exports.implementationRuleAssertionSlug = implementationRuleAssertionSlug;
		const IMPLEMENTATION_RULES_REQUIREMENT_ID = 'req_cadstromimr0000000000000000000000000';
		exports.ImplementationRulesRequirement = {
		    id: IMPLEMENTATION_RULES_REQUIREMENT_ID,
		    slug: 'cadstrom.implementation_rules',
		    slugPrefix: 'IMR',
		    name: 'Implementation Rules',
		    description: 'Checks each build module against the implementation rules of the package it resolves to. Each rule is its own assertion definition, minted per package.',
		    assertionsEnumerable: false,
		};
		function implementationRuleAssertionSlug(packageId, ruleId) {
		    return `${exports.ImplementationRulesRequirement.slug}.${packageId}.${ruleId}`;
		}
		
	} (implementationRules$1));
	return implementationRules$1;
}

var logicalBus = {};

var hasRequiredLogicalBus;

function requireLogicalBus () {
	if (hasRequiredLogicalBus) return logicalBus;
	hasRequiredLogicalBus = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.LogicalBusAssertions = exports.Rs485DependentSignals = exports.EthernetDependentSignals = exports.UsbDependentSignals = exports.SpiNoChipSelectContention = exports.SpiChipSelectDriven = exports.SpiSingleController = exports.I2cNoSharedAddress = exports.I2cAddressResolvable = exports.I2cLinesPulledUp = exports.I2cHasController = exports.DevicesConfigured = exports.AssignmentsValid = exports.RequiredSignalsAssigned = exports.LogicalBusRequirement = void 0;
		const enums_1 = requireEnums();
		const LOGICAL_BUS_REQUIREMENT_ID = 'req_cadstrombus0000000000000000000000000';
		exports.LogicalBusRequirement = {
		    id: LOGICAL_BUS_REQUIREMENT_ID,
		    slug: 'cadstrom.bus',
		    slugPrefix: 'BUS',
		    name: 'Logical Bus',
		    description: 'Asserts that a recognized bus interface — a set of nets and the device functions assigned to them (I²C, SPI, UART, USB, HDMI, Ethernet, …) — is correctly and completely wired: every participating device assigns the bus’s required signals, the assignments and operating states are valid, and protocol-specific rules (controller configuration, addressing, selection, required bias components) are satisfied. Evaluated once per bus.',
		};
		exports.RequiredSignalsAssigned = {
		    id: 'asrt_busreqsigassigned000000000000000000',
		    slug: 'cadstrom.bus.common.required_signals_assigned',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'Every required bus signal must be assigned',
		    description: 'Every member of the bus must assign a component signal to each required signal of its bus function. A member that leaves a required bus signal unassigned cannot participate in the interface.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.AssignmentsValid = {
		    id: 'asrt_busassignvalid000000000000000000000',
		    slug: 'cadstrom.bus.common.assignments_valid',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'Bus signal assignments must be valid',
		    description: 'Every assigned component signal must be a legal target for its bus signal; a signal that does not permit multiple assignments must have exactly one, and no signal may exceed its maximum number of assignments.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.DevicesConfigured = {
		    id: 'asrt_busdevconfig00000000000000000000000',
		    slug: 'cadstrom.bus.common.devices_configured',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'Bus devices must be properly configured to participate in the bus',
		    description: 'Every member’s function state and mode state must be valid for it to act on the bus in the evaluated circuit state. A member held in a state in which its bus function is disabled cannot participate.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.I2cHasController = {
		    id: 'asrt_busi2chascontroller0000000000000000',
		    slug: 'cadstrom.bus.i2c.has_controller',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'An I²C bus must have at least one controller',
		    description: 'An I²C bus must have a controller. I²C permits multiple controllers (multi-master), so any count of one or more is valid; a bus with only peripherals has nothing to initiate transfers.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.I2cLinesPulledUp = {
		    id: 'asrt_busi2cpullups0000000000000000000000',
		    slug: 'cadstrom.bus.i2c.lines_pulled_up',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'I²C SDA and SCL must be biased with pull-up resistors',
		    description: 'The open-drain `SDA` and `SCL` lines must each have the pull-up resistor to a supply required to return them to their idle high state.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.I2cAddressResolvable = {
		    id: 'asrt_busi2caddrresolvable000000000000000',
		    slug: 'cadstrom.bus.i2c.address_resolvable',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'Every I²C peripheral must have a resolvable address',
		    description: 'Each peripheral must expose the address information required to compute its bus address in the evaluated circuit state (fixed, selectable, or configurable). A peripheral whose address cannot be resolved cannot be reliably addressed.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.I2cNoSharedAddress = {
		    id: 'asrt_busi2cnosharedaddr00000000000000000',
		    slug: 'cadstrom.bus.i2c.no_shared_address',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'No two I²C devices may share the same address',
		    description: 'A controller must not resolve two peripherals to the same address on the same bus lines. Two devices answering the same address collide and cannot be independently accessed.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.SpiSingleController = {
		    id: 'asrt_busspisinglecontroller0000000000000',
		    slug: 'cadstrom.bus.spi.single_controller',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'An SPI bus must have exactly one controller',
		    description: 'SPI is a single-controller bus. A bus with zero controllers has nothing to initiate transfers; a bus with more than one controller has contending masters.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.SpiChipSelectDriven = {
		    id: 'asrt_busspicsdriven000000000000000000000',
		    slug: 'cadstrom.bus.spi.chip_select_driven',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'Every SPI peripheral must be selected by a controller-driven chip-select',
		    description: 'Each peripheral’s chip-select line must be driven by a controller, not hardwired to a fixed level. A hardcoded select cannot be deasserted, so the peripheral cannot be multiplexed onto the bus.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.SpiNoChipSelectContention = {
		    id: 'asrt_busspinocscontention000000000000000',
		    slug: 'cadstrom.bus.spi.no_chip_select_contention',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'No two SPI peripherals may share the same chip-select selection',
		    description: 'No two peripherals may be selected by the same chip-select state from a controller. Peripherals sharing an active selection contend when that select is asserted.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.UsbDependentSignals = {
		    id: 'asrt_bususbdepsignals0000000000000000000',
		    slug: 'cadstrom.bus.usb.dependent_signals_assigned',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'All dependent USB signals must be assigned together',
		    description: 'A USB interface must assign its full co-required signal set — at minimum the `D+` / `D-` data pair (USB 3.x additionally requires the SuperSpeed `SSTX±` / `SSRX±` pairs). A partially-assigned data pair is an incomplete implementation.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.EthernetDependentSignals = {
		    id: 'asrt_busethdepsignals0000000000000000000',
		    slug: 'cadstrom.bus.ethernet.dependent_signals_assigned',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'All dependent Ethernet signals must be assigned together',
		    description: 'A multi-pair Ethernet interface must assign its complete signal set — a 1000BASE-T link requires all four differential pairs `MDI0±`…`MDI3±`.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.Rs485DependentSignals = {
		    id: 'asrt_busrs485depsignals00000000000000000',
		    slug: 'cadstrom.bus.rs485.dependent_signals_assigned',
		    requirementDefinitionId: LOGICAL_BUS_REQUIREMENT_ID,
		    name: 'All dependent RS-485 signals must be assigned together',
		    description: 'A full-duplex RS-485 interface must assign both co-required differential pairs — the driver pair (`Y`/`Z`) and the receiver pair (`A`/`B`).',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LogicalBusAssertions = [
		    exports.RequiredSignalsAssigned,
		    exports.AssignmentsValid,
		    exports.DevicesConfigured,
		    exports.I2cHasController,
		    exports.I2cLinesPulledUp,
		    exports.I2cAddressResolvable,
		    exports.I2cNoSharedAddress,
		    exports.SpiSingleController,
		    exports.SpiChipSelectDriven,
		    exports.SpiNoChipSelectContention,
		    exports.UsbDependentSignals,
		    exports.EthernetDependentSignals,
		    exports.Rs485DependentSignals,
		];
		
	} (logicalBus));
	return logicalBus;
}

var pinout = {};

var hasRequiredPinout;

function requirePinout () {
	if (hasRequiredPinout) return pinout;
	hasRequiredPinout = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PinoutAssertions = exports.PinoutPackagePinMapped = exports.PinoutDesignPinMatchesPackagePin = exports.PinoutDesignSignalMapped = exports.PinoutRequirement = void 0;
		const enums_1 = requireEnums();
		const PINOUT_REQUIREMENT_ID = 'req_cadstrompinout0000000000000000000000';
		exports.PinoutRequirement = {
		    id: PINOUT_REQUIREMENT_ID,
		    slug: 'cadstrom.pinout',
		    slugPrefix: 'PINOUT',
		    name: 'Pinout',
		    description: 'Asserts that each design signal is mapped to a package signal, that the design pins match the package pins for that signal, and that every package pin is represented by a design signal. Evaluated once per component instance.',
		};
		exports.PinoutDesignSignalMapped = {
		    id: 'asrt_pinoutdesignmapped000000000000000000',
		    slug: 'cadstrom.pinout.design_signal.mapped',
		    requirementDefinitionId: PINOUT_REQUIREMENT_ID,
		    name: 'Design signal must be mapped to a package signal',
		    description: 'A design signal on a package-backed component must map to a package-store signal.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PinoutDesignPinMatchesPackagePin = {
		    id: 'asrt_pinoutpinmatch0000000000000000000000',
		    slug: 'cadstrom.pinout.design_pin.matches_package_pin',
		    requirementDefinitionId: PINOUT_REQUIREMENT_ID,
		    name: 'Design pin must match its mapped package pin',
		    description: 'When a design signal maps to a package-store signal, the design pin number must match the package pin number for that signal.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PinoutPackagePinMapped = {
		    id: 'asrt_pinoutpackagemapped00000000000000000',
		    slug: 'cadstrom.pinout.package_pin.mapped',
		    requirementDefinitionId: PINOUT_REQUIREMENT_ID,
		    name: 'Package pin must be represented by a design signal',
		    description: 'A package-store pin that is part of the resolved package pinout must be represented by a design signal mapping.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PinoutAssertions = [
		    exports.PinoutDesignSignalMapped,
		    exports.PinoutDesignPinMatchesPackagePin,
		    exports.PinoutPackagePinMapped,
		];
		
	} (pinout));
	return pinout;
}

var powerArchitecture = {};

var hasRequiredPowerArchitecture;

function requirePowerArchitecture () {
	if (hasRequiredPowerArchitecture) return powerArchitecture;
	hasRequiredPowerArchitecture = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PowerArchitectureAssertions = exports.PowerArchitecturePmicAssertions = exports.PowerArchitectureProjectAssertions = exports.ProtectionInputProtectionEffective = exports.ChargerBatterySafeguardsConfigured = exports.LdoHeadroomSufficient = exports.StageEcRespected = exports.StageAmrRespected = exports.StageStatusOutputsTerminated = exports.StageEnableInputsTerminated = exports.StageSupportComponentsPresent = exports.StageCurrentLimitConfigured = exports.StageOutputVoltageConfigured = exports.LoadSupplyWithinRatings = exports.RailOrderingDefined = exports.RailSourcesIdentified = exports.PowerArchitectureRequirement = void 0;
		const enums_1 = requireEnums();
		const POWER_ARCHITECTURE_REQUIREMENT_ID = 'req_cadstrompower00000000000000000000000';
		exports.PowerArchitectureRequirement = {
		    id: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    slug: 'cadstrom.power',
		    slugPrefix: 'PWR',
		    name: 'Power Architecture',
		    description: 'Asserts that the project’s power architecture — every input source, conversion stage, protection device, and rail — is correctly configured, sequenced, and operated within its ratings. Project-wide checks are evaluated once per build; per-stage checks are evaluated once per power-stage device.',
		};
		exports.RailSourcesIdentified = {
		    id: 'asrt_pwrrailsourcesident00000000000000000',
		    slug: 'cadstrom.power.rails.sources_identified',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Every power rail must have an identified source',
		    description: 'Every power-carrying net must trace to the source that drives it — an input source (connector, battery), the output of a conversion stage, or an off-board supply — through any series elements (fuses, ferrites, diodes, 0 Ω links, load switches). A rail whose source cannot be established indicates a broken or unmappable power chain, and every downstream conclusion about that rail is unreliable.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.RailOrderingDefined = {
		    id: 'asrt_pwrseqrailorder000000000000000000000',
		    slug: 'cadstrom.power.sequencing.rail_ordering_defined',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Required rail ordering must be enforced',
		    description: 'Where a stage or load requires ordered bring-up (a controller supplied before its enable asserts, a core rail before an I/O rail), the order must be enforced in hardware (power-good cascade, daisy-chained enables) or be achievable by the documented firmware control of the enables. Ordering left to chance produces intermittent start-up failures.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LoadSupplyWithinRatings = {
		    id: 'asrt_pwrloadsupplyrange000000000000000000',
		    slug: 'cadstrom.power.loads.supply_within_ratings',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Every load’s supply must stay within its rated range',
		    description: 'The worst-case voltage of every rail — including regulator reference tolerance and divider tolerance — must stay inside the recommended operating range of every load on the rail. A rail that drifts past a load’s recommended maximum shortens its life and voids its electrical specifications.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageOutputVoltageConfigured = {
		    id: 'asrt_pwrstageoutputvolt000000000000000000',
		    slug: 'cadstrom.power.stage.output_voltage_configured',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Every converter must be configured to produce its intended output voltage',
		    description: 'The stage’s programmed output — feedback divider, strap pins, or digital configuration — must produce the rail voltage its downstream loads require, including worst-case reference and divider tolerance. A mis-programmed divider ships the wrong voltage to every load on the rail.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageCurrentLimitConfigured = {
		    id: 'asrt_pwrstagecurrentlimit0000000000000000',
		    slug: 'cadstrom.power.stage.current_limit_configured',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Every configurable current limit must match the expected load',
		    description: 'Where the stage exposes a configurable current limit (ILIM resistor, strap, or register default), the configured limit must cover the rail’s expected load current without exceeding what the upstream source can supply. A limit set too low browns out under peak load; one set too high defeats the protection.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageSupportComponentsPresent = {
		    id: 'asrt_pwrstagesupportcomp00000000000000000',
		    slug: 'cadstrom.power.stage.support_components_present',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Datasheet-required support components must be present and correctly valued',
		    description: 'Every support component the stage’s datasheet requires — bootstrap capacitor, compensation network, minimum input/output capacitance — must be populated with values in the datasheet-permitted range. A missing bootstrap capacitor or under-specified output capacitance prevents switching or destabilizes the loop.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageEnableInputsTerminated = {
		    id: 'asrt_pwrstageenableterm000000000000000000',
		    slug: 'cadstrom.power.stage.enable_inputs_terminated',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Every enable and mode input must have a defined state',
		    description: 'Every enable, mode, or control input of the stage must be at a defined logic level whenever its supply is present — via pull resistor, hard strap, or a driver that is never high-impedance. An enable left floating while the controlling MCU is in reset gives the stage an undefined power-up state.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageStatusOutputsTerminated = {
		    id: 'asrt_pwrstagestatusterm000000000000000000',
		    slug: 'cadstrom.power.stage.status_outputs_terminated',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Used status outputs must be correctly terminated',
		    description: 'Every open-drain status output (power-good, charge status, fault/interrupt) that the design consumes must have a pull-up to a rail valid at the reading logic. An unpulled open-drain output never presents a logic high, so the consumer reads a constant state.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageAmrRespected = {
		    id: 'asrt_pwrstageamr0000000000000000000000000',
		    slug: 'cadstrom.power.stage.amr_respected',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'No operating point may exceed absolute maximum ratings',
		    description: 'No pin of the stage may see a voltage or current beyond its absolute maximum rating under any reachable operating condition, including start-up, fault, and OTG/reverse modes. Exceeding an absolute maximum risks permanent damage, not merely degraded performance.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.StageEcRespected = {
		    id: 'asrt_pwrstageec00000000000000000000000000',
		    slug: 'cadstrom.power.stage.ec_respected',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Power stages must operate within recommended conditions',
		    description: 'The stage must operate inside its recommended operating conditions at the configured operating point. Operation outside the recommended range is unspecified even when below absolute maximums — the vendor guarantees no parameter there.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.LdoHeadroomSufficient = {
		    id: 'asrt_pwrldoheadroom0000000000000000000000',
		    slug: 'cadstrom.power.ldo.headroom_sufficient',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Every LDO must have sufficient input-output headroom',
		    description: 'A linear regulator’s input-to-output differential must exceed its datasheet dropout voltage at the expected load current. In dropout the regulator stops regulating — the output tracks the input minus the pass-element drop, and supply rejection collapses.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ChargerBatterySafeguardsConfigured = {
		    id: 'asrt_pwrchgrbattsafeguards000000000000000',
		    slug: 'cadstrom.power.charger.battery_safeguards_configured',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Battery charging must have its safety functions configured',
		    description: 'A battery charging stage must have its protective functions configured for the pack it charges: temperature sensing (a real thermistor on the NTC input, not a fixed divider), input and charge current limits appropriate to the source and cell, and correct termination behaviour. Missing safeguards allow charging outside the pack’s safe envelope.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.ProtectionInputProtectionEffective = {
		    id: 'asrt_pwrprotinputprot00000000000000000000',
		    slug: 'cadstrom.power.protection.input_protection_effective',
		    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
		    name: 'Input protection devices must be effective as populated',
		    description: 'Reverse-polarity and isolation devices must be fully effective in the populated topology: a P-FET’s gate bias must fully enhance it at operating voltages, an N-FET’s gate drive must exceed its source, and clamps must cover the applied range. A partially-enhanced pass device dissipates excess power and defeats the protection it implements.',
		    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
		};
		exports.PowerArchitectureProjectAssertions = [
		    exports.RailSourcesIdentified,
		    exports.RailOrderingDefined,
		    exports.LoadSupplyWithinRatings,
		];
		exports.PowerArchitecturePmicAssertions = [
		    exports.StageOutputVoltageConfigured,
		    exports.StageCurrentLimitConfigured,
		    exports.StageSupportComponentsPresent,
		    exports.StageEnableInputsTerminated,
		    exports.StageStatusOutputsTerminated,
		    exports.StageAmrRespected,
		    exports.StageEcRespected,
		    exports.LdoHeadroomSufficient,
		    exports.ChargerBatterySafeguardsConfigured,
		    exports.ProtectionInputProtectionEffective,
		];
		exports.PowerArchitectureAssertions = [
		    ...exports.PowerArchitectureProjectAssertions,
		    ...exports.PowerArchitecturePmicAssertions,
		];
		
	} (powerArchitecture));
	return powerArchitecture;
}

var hasRequiredCanonicalDefinitions;

function requireCanonicalDefinitions () {
	if (hasRequiredCanonicalDefinitions) return canonicalDefinitions;
	hasRequiredCanonicalDefinitions = 1;
	(function (exports) {
		var __createBinding = (canonicalDefinitions && canonicalDefinitions.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (canonicalDefinitions && canonicalDefinitions.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireComponentFunction(), exports);
		__exportStar(requireComponentLimits(), exports);
		__exportStar(requireComponentResource(), exports);
		__exportStar(requireComponentValues(), exports);
		__exportStar(requireDescriptor_types(), exports);
		__exportStar(requireDifferentialPair(), exports);
		__exportStar(requireElectricalInterfaceCompatibility(), exports);
		__exportStar(requireImplementationRules$2(), exports);
		__exportStar(requireLogicalBus(), exports);
		__exportStar(requirePinout(), exports);
		__exportStar(requirePowerArchitecture(), exports);
		
	} (canonicalDefinitions));
	return canonicalDefinitions;
}

var canonicalIds = {};

var hasRequiredCanonicalIds;

function requireCanonicalIds () {
	if (hasRequiredCanonicalIds) return canonicalIds;
	hasRequiredCanonicalIds = 1;
	Object.defineProperty(canonicalIds, "__esModule", { value: true });
	
	return canonicalIds;
}

var continuityKey = {};

var hasRequiredContinuityKey;

function requireContinuityKey () {
	if (hasRequiredContinuityKey) return continuityKey;
	hasRequiredContinuityKey = 1;
	Object.defineProperty(continuityKey, "__esModule", { value: true });
	continuityKey.ContinuityKeySchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const enums_1 = requireEnums();
	continuityKey.ContinuityKeySchema = zod_1.z.object({
	    type: enums_1.ContinuityKeyTypeSchema,
	    value: zod_1.z.string(),
	});
	
	return continuityKey;
}

var findingDescriptor = {};

var hasRequiredFindingDescriptor;

function requireFindingDescriptor () {
	if (hasRequiredFindingDescriptor) return findingDescriptor;
	hasRequiredFindingDescriptor = 1;
	Object.defineProperty(findingDescriptor, "__esModule", { value: true });
	findingDescriptor.findingDescriptorSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const enums_1 = requireEnums();
	findingDescriptor.findingDescriptorSchema = zod_1.z.object({
	    id: zod_1.z.string(),
	    findingNumber: zod_1.z.number(),
	    severity: enums_1.RequirementSeveritySchema,
	    message: zod_1.z.string().nullable(),
	    description: zod_1.z.string().nullable(),
	});
	
	return findingDescriptor;
}

var findingSlug = {};

var hasRequiredFindingSlug;

function requireFindingSlug () {
	if (hasRequiredFindingSlug) return findingSlug;
	hasRequiredFindingSlug = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FINDING_SLUG_PREFIX = void 0;
		exports.formatFindingSlug = formatFindingSlug;
		exports.parseFindingSlug = parseFindingSlug;
		exports.FINDING_SLUG_PREFIX = 'FINDING-';
		function formatFindingSlug(findingNumber) {
		    return `${exports.FINDING_SLUG_PREFIX}${findingNumber}`;
		}
		function parseFindingSlug(slug) {
		    if (!slug.startsWith(exports.FINDING_SLUG_PREFIX)) {
		        return null;
		    }
		    const rest = slug.slice(exports.FINDING_SLUG_PREFIX.length);
		    if (!/^[1-9]\d*$/.test(rest)) {
		        return null;
		    }
		    return Number.parseInt(rest, 10);
		}
		
	} (findingSlug));
	return findingSlug;
}

var logPayloads = {};

var noteLogPayload = {};

var hasRequiredNoteLogPayload;

function requireNoteLogPayload () {
	if (hasRequiredNoteLogPayload) return noteLogPayload;
	hasRequiredNoteLogPayload = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.noteLogPayloadSchema = exports.userNoteLogPayloadSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.userNoteLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('user-note'),
		    body: zod_1.z.string().min(1),
		});
		exports.noteLogPayloadSchema = exports.userNoteLogPayloadSchema;
		
	} (noteLogPayload));
	return noteLogPayload;
}

var updateLogPayload = {};

var hasRequiredUpdateLogPayload;

function requireUpdateLogPayload () {
	if (hasRequiredUpdateLogPayload) return updateLogPayload;
	hasRequiredUpdateLogPayload = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.UpdateLogPayloadSchema = exports.findingMergedFromAnalysisUpdateLogPayloadSchema = exports.findingUnlinkedAssertionResultsUpdateLogPayloadSchema = exports.findingLinkedAssertionResultsUpdateLogPayloadSchema = exports.findingCreatedWithAssertionResultsUpdateLogPayloadSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.findingCreatedWithAssertionResultsUpdateLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('finding-created-with-assertion-results'),
		    assertionResultIds: zod_1.z.array(zod_1.z.string()),
		});
		exports.findingLinkedAssertionResultsUpdateLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('finding-linked-assertion-results'),
		    assertionResultIds: zod_1.z.array(zod_1.z.string()),
		});
		exports.findingUnlinkedAssertionResultsUpdateLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('finding-unlinked-assertion-results'),
		    assertionResultIds: zod_1.z.array(zod_1.z.string()),
		});
		exports.findingMergedFromAnalysisUpdateLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('finding-merged-from-analysis'),
		});
		exports.UpdateLogPayloadSchema = zod_1.z.discriminatedUnion('kind', [
		    exports.findingCreatedWithAssertionResultsUpdateLogPayloadSchema,
		    exports.findingLinkedAssertionResultsUpdateLogPayloadSchema,
		    exports.findingMergedFromAnalysisUpdateLogPayloadSchema,
		    exports.findingUnlinkedAssertionResultsUpdateLogPayloadSchema,
		]);
		
	} (updateLogPayload));
	return updateLogPayload;
}

var continuityLogPayload = {};

var hasRequiredContinuityLogPayload;

function requireContinuityLogPayload () {
	if (hasRequiredContinuityLogPayload) return continuityLogPayload;
	hasRequiredContinuityLogPayload = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.continuityLogPayloadSchema = exports.findingContinuityLogPayloadSchema = exports.assertionResultContinuityLogPayloadSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const enums_1 = requireEnums();
		exports.assertionResultContinuityLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('assertion-result-continuity'),
		    previousAssertionResultId: zod_1.z.string(),
		    previousBuildId: zod_1.z.string(),
		    inheritedWorkflowState: enums_1.RequirementWorkflowStateSchema.nullable(),
		});
		exports.findingContinuityLogPayloadSchema = zod_1.z.object({
		    kind: zod_1.z.literal('finding-continuity'),
		    findingId: zod_1.z.string(),
		    newAssertionResultId: zod_1.z.string().nullable(),
		    previousAssertionResultId: zod_1.z.string().nullable(),
		    previousBuildId: zod_1.z.string().nullable(),
		    disposition: enums_1.FindingContinuityDispositionSchema.default(enums_1.FindingContinuityDisposition.Reproduced),
		    reason: zod_1.z.string().nullable().default(null),
		});
		exports.continuityLogPayloadSchema = zod_1.z.discriminatedUnion('kind', [
		    exports.assertionResultContinuityLogPayloadSchema,
		    exports.findingContinuityLogPayloadSchema,
		]);
		
	} (continuityLogPayload));
	return continuityLogPayload;
}

var validationResultLogPayload = {};

var hasRequiredValidationResultLogPayload;

function requireValidationResultLogPayload () {
	if (hasRequiredValidationResultLogPayload) return validationResultLogPayload;
	hasRequiredValidationResultLogPayload = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.validationResultLogPayloadSchema = exports.aiValidationExecutionPayloadSchema = exports.aiValidationExecutionObservationSchema = exports.aiValidationExecutionStatusSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.aiValidationExecutionStatusSchema = zod_1.z.enum(['succeeded', 'failed']);
		exports.aiValidationExecutionObservationSchema = zod_1.z.object({
		    conversationId: zod_1.z.string(),
		    sessionId: zod_1.z.string(),
		    agentId: zod_1.z.string(),
		    triggerId: zod_1.z.string(),
		    analysisRunId: zod_1.z.string().optional(),
		    subject: zod_1.z
		        .object({
		        typeName: zod_1.z.string(),
		        subjectKey: zod_1.z.string().optional(),
		        subjectFingerprint: zod_1.z.string().optional(),
		    })
		        .optional(),
		    startedAt: zod_1.z.string(),
		    completedAt: zod_1.z.string(),
		    status: exports.aiValidationExecutionStatusSchema,
		    error: zod_1.z
		        .object({
		        name: zod_1.z.string().optional(),
		        message: zod_1.z.string(),
		    })
		        .optional(),
		});
		exports.aiValidationExecutionPayloadSchema = zod_1.z.object({
		    agentExecution: exports.aiValidationExecutionObservationSchema,
		});
		exports.validationResultLogPayloadSchema = zod_1.z.object({
		    generatedAt: zod_1.z.string(),
		    configurationSnapshot: zod_1.z.unknown().optional(),
		    rawPayloadRef: zod_1.z.string().nullable(),
		    agentExecution: exports.aiValidationExecutionObservationSchema.optional(),
		});
		
	} (validationResultLogPayload));
	return validationResultLogPayload;
}

var hasRequiredLogPayloads;

function requireLogPayloads () {
	if (hasRequiredLogPayloads) return logPayloads;
	hasRequiredLogPayloads = 1;
	(function (exports) {
		var __createBinding = (logPayloads && logPayloads.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (logPayloads && logPayloads.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireNoteLogPayload(), exports);
		__exportStar(requireUpdateLogPayload(), exports);
		__exportStar(requireContinuityLogPayload(), exports);
		__exportStar(requireValidationResultLogPayload(), exports);
		
	} (logPayloads));
	return logPayloads;
}

var RequirementEvaluationBatchV2 = {};

var DesignObject = {};

var hasRequiredDesignObject;

function requireDesignObject () {
	if (hasRequiredDesignObject) return DesignObject;
	hasRequiredDesignObject = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DesignObjectSchema = exports.DesignObjectTypeSchema = exports.DesignObjectType = void 0;
		exports.designObjectKey = designObjectKey;
		const zod_1 = /*@__PURE__*/ requireZod();
		var DesignObjectType;
		(function (DesignObjectType) {
		    DesignObjectType["Net"] = "net";
		    DesignObjectType["Module"] = "module";
		    DesignObjectType["Component"] = "component";
		    DesignObjectType["NetConn"] = "netconn";
		    DesignObjectType["Custom"] = "custom";
		})(DesignObjectType || (exports.DesignObjectType = DesignObjectType = {}));
		exports.DesignObjectTypeSchema = zod_1.z.nativeEnum(DesignObjectType);
		const nullToUndefined = (val) => (val === null ? undefined : val);
		exports.DesignObjectSchema = zod_1.z.object({
		    type: exports.DesignObjectTypeSchema,
		    net: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    module: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    component: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    signal: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    customTypeName: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    subjectName: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    description: zod_1.z.preprocess(nullToUndefined, zod_1.z.string().optional()),
		    properties: zod_1.z.preprocess(nullToUndefined, zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional()),
		});
		function designObjectKey(obj) {
		    return `${obj.type}|${obj.net ?? ''}|${obj.module ?? ''}|${obj.component ?? ''}|${obj.signal ?? ''}|${obj.customTypeName ?? ''}|${obj.subjectName ?? ''}`;
		}
		
	} (DesignObject));
	return DesignObject;
}

var hasRequiredRequirementEvaluationBatchV2;

function requireRequirementEvaluationBatchV2 () {
	if (hasRequiredRequirementEvaluationBatchV2) return RequirementEvaluationBatchV2;
	hasRequiredRequirementEvaluationBatchV2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RequirementEvaluationBatchV2Schema = exports.GroupedLogEntryInputSchema = exports.AssertionEvaluationResultV2Schema = exports.RequirementEvidenceInputSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const DesignObject_1 = requireDesignObject();
		const continuity_key_1 = requireContinuityKey();
		const enums_1 = requireEnums();
		exports.RequirementEvidenceInputSchema = zod_1.z.object({
		    kind: zod_1.z.string(),
		    summary: zod_1.z.string(),
		    payload: zod_1.z.unknown().optional(),
		    rawPayloadRef: zod_1.z.string().optional(),
		    contentHash: zod_1.z.string().optional(),
		});
		exports.AssertionEvaluationResultV2Schema = zod_1.z.object({
		    assertionDefinitionId: zod_1.z.string(),
		    resultSlug: zod_1.z.string(),
		    continuityKey: continuity_key_1.ContinuityKeySchema.optional(),
		    designObjects: DesignObject_1.DesignObjectSchema.array(),
		    severity: enums_1.RequirementSeveritySchema,
		    message: zod_1.z.string(),
		    description: zod_1.z.string().optional(),
		    evidence: exports.RequirementEvidenceInputSchema.array().optional(),
		    configurationSnapshot: zod_1.z.unknown().optional(),
		    rawPayloadRef: zod_1.z.string().optional(),
		    sourceResultKey: zod_1.z
		        .string()
		        .describe('Stable per-result key. Together with the batch-level `sourceSystem` and `sourceRunId`, anchors idempotency: a retry of the same logical write yields the same row.'),
		});
		exports.GroupedLogEntryInputSchema = zod_1.z.object({
		    sourceSystem: zod_1.z.enum(['sigma-engine', 'ai-analysis', 'legacy-migration']),
		    sourceRunId: zod_1.z.string(),
		    sourceResultKey: zod_1.z
		        .string()
		        .describe('Per-log-entry idempotency anchor (e.g. "deterministic"). Together with (sourceSystem, sourceRunId) it identifies the single log entry that all of this batch\'s ARs link to.'),
		    deterministicValidationPayload: zod_1.z.unknown().optional(),
		    aiValidationPayload: zod_1.z.unknown().optional(),
		    message: zod_1.z.string().optional(),
		    description: zod_1.z.string().optional(),
		});
		exports.RequirementEvaluationBatchV2Schema = zod_1.z.object({
		    projectId: zod_1.z.string(),
		    buildId: zod_1.z.string(),
		    engineRunId: zod_1.z
		        .string()
		        .describe("The source system's run identifier — resolves to `sourceRunId` on each result row for idempotency."),
		    sourceSystem: zod_1.z
		        .enum(['sigma-engine', 'ai-analysis', 'legacy-migration'])
		        .describe('Origin of the batch. Part of the source-key idempotency tuple (sourceSystem, sourceRunId, sourceResultKey).'),
		    generatedAt: zod_1.z.string(),
		    recordedAt: zod_1.z
		        .string()
		        .optional()
		        .describe('When the recorded events occurred (ISO 8601). Stamped as the createdAt of the rows this batch creates; defaults to write time. The legacy migration sets it to the original analysis timestamp.'),
		    assertionResults: exports.AssertionEvaluationResultV2Schema.array(),
		    groupedLogEntry: exports.GroupedLogEntryInputSchema.optional(),
		});
		
	} (RequirementEvaluationBatchV2));
	return RequirementEvaluationBatchV2;
}

var hasRequiredRequirementsV2$1;

function requireRequirementsV2$1 () {
	if (hasRequiredRequirementsV2$1) return RequirementsV2$1;
	hasRequiredRequirementsV2$1 = 1;
	(function (exports) {
		var __createBinding = (RequirementsV2$1 && RequirementsV2$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (RequirementsV2$1 && RequirementsV2$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireCanonicalDefinitions(), exports);
		__exportStar(requireCanonicalIds(), exports);
		__exportStar(requireContinuityKey(), exports);
		__exportStar(requireEnums(), exports);
		__exportStar(requireFindingDescriptor(), exports);
		__exportStar(requireFindingSlug(), exports);
		__exportStar(requireLogPayloads(), exports);
		__exportStar(requireRequirementEvaluationBatchV2(), exports);
		__exportStar(requireSeverityNames(), exports);
		
	} (RequirementsV2$1));
	return RequirementsV2$1;
}

var AI = {};

var DatasheetContentLabels = {};

var hasRequiredDatasheetContentLabels;

function requireDatasheetContentLabels () {
	if (hasRequiredDatasheetContentLabels) return DatasheetContentLabels;
	hasRequiredDatasheetContentLabels = 1;
	Object.defineProperty(DatasheetContentLabels, "__esModule", { value: true });
	DatasheetContentLabels.DatasheetContentLabelIds = void 0;
	var DatasheetContentLabelIds;
	(function (DatasheetContentLabelIds) {
	    DatasheetContentLabelIds["PinDefinitions"] = "pin-definitions";
	    DatasheetContentLabelIds["PinFunctions"] = "pin-functions";
	    DatasheetContentLabelIds["PinoutDiagram"] = "pinout-diagram";
	    DatasheetContentLabelIds["AbsoluteMaximumRatings"] = "absolute-maximum-ratings";
	    DatasheetContentLabelIds["OperatingConditions"] = "operating-conditions";
	    DatasheetContentLabelIds["ElectricalCharacteristics"] = "electrical-characteristics";
	    DatasheetContentLabelIds["BlockDiagram"] = "block-diagram";
	    DatasheetContentLabelIds["MemoryRegister"] = "memory-register";
	    DatasheetContentLabelIds["IoPorts"] = "io-ports";
	    DatasheetContentLabelIds["ApplicationInformation"] = "application-information";
	    DatasheetContentLabelIds["ComponentFunctions"] = "component-functions";
	    DatasheetContentLabelIds["PackageInformation"] = "package-information";
	    DatasheetContentLabelIds["Errata"] = "errata";
	    DatasheetContentLabelIds["RevisionHistory"] = "revision-history";
	    DatasheetContentLabelIds["TableOfContents"] = "table-of-contents";
	    DatasheetContentLabelIds["_TableOfContentsOld"] = "toc";
	    DatasheetContentLabelIds["ThermalInformation"] = "thermal-information";
	    DatasheetContentLabelIds["DevelopmentTools"] = "development-tools";
	    DatasheetContentLabelIds["Glossary"] = "glossary";
	    DatasheetContentLabelIds["ContextCaption"] = "context-caption";
	})(DatasheetContentLabelIds || (DatasheetContentLabels.DatasheetContentLabelIds = DatasheetContentLabelIds = {}));
	
	return DatasheetContentLabels;
}

var PdfElement = {};

var BoundingBox = {};

var hasRequiredBoundingBox;

function requireBoundingBox () {
	if (hasRequiredBoundingBox) return BoundingBox;
	hasRequiredBoundingBox = 1;
	Object.defineProperty(BoundingBox, "__esModule", { value: true });
	BoundingBox.boundingBoxSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	BoundingBox.boundingBoxSchema = zod_1.z.object({
	    ulx: zod_1.z.number().describe('The x coordinate of the upper left corner of the bounding box'),
	    uly: zod_1.z.number().describe('The y coordinate of the upper left corner of the bounding box'),
	    lrx: zod_1.z.number().describe('The x coordinate of the lower right corner of the bounding box'),
	    lry: zod_1.z.number().describe('The y coordinate of the lower right corner of the bounding box'),
	});
	
	return BoundingBox;
}

var hasRequiredPdfElement;

function requirePdfElement () {
	if (hasRequiredPdfElement) return PdfElement;
	hasRequiredPdfElement = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PdfElementDescriptionSchema = exports.PdfElementSchema = exports.PdfTableFormatSchema = exports.PdfTableFormat = exports.PdfElementTypeSchema = exports.PdfElementType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundingBox_1 = requireBoundingBox();
		var PdfElementType;
		(function (PdfElementType) {
		    PdfElementType["List"] = "list";
		    PdfElementType["Table"] = "table";
		    PdfElementType["Text"] = "text";
		    PdfElementType["Title"] = "title";
		    PdfElementType["Figure"] = "figure";
		})(PdfElementType || (exports.PdfElementType = PdfElementType = {}));
		exports.PdfElementTypeSchema = zod_1.z.nativeEnum(PdfElementType);
		var PdfTableFormat;
		(function (PdfTableFormat) {
		    PdfTableFormat["Json"] = "json";
		    PdfTableFormat["Markdown"] = "markdown";
		})(PdfTableFormat || (exports.PdfTableFormat = PdfTableFormat = {}));
		exports.PdfTableFormatSchema = zod_1.z.nativeEnum(PdfTableFormat);
		exports.PdfElementSchema = zod_1.z.object({
		    type: exports.PdfElementTypeSchema,
		    pageNumber: zod_1.z.number(),
		    text: zod_1.z.string().optional(),
		    base64CropImage: zod_1.z.string().optional(),
		    boundingBox: BoundingBox_1.boundingBoxSchema.optional(),
		});
		exports.PdfElementDescriptionSchema = zod_1.z.object({
		    shortDescription: zod_1.z
		        .string()
		        .describe('A single sentence (maximum two sentences) summary of what the element depicts'),
		    longDescription: zod_1.z
		        .string()
		        .describe('A detailed description focusing on what the element depicts, its key elements, and any relevant technical details'),
		});
		
	} (PdfElement));
	return PdfElement;
}

var hasRequiredAI;

function requireAI () {
	if (hasRequiredAI) return AI;
	hasRequiredAI = 1;
	(function (exports) {
		var __createBinding = (AI && AI.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (AI && AI.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireDatasheetContentLabels(), exports);
		__exportStar(requirePdfElement(), exports);
		
	} (AI));
	return AI;
}

var AnalysisRequirements = {};

var AnalysisType = {};

var hasRequiredAnalysisType$1;

function requireAnalysisType$1 () {
	if (hasRequiredAnalysisType$1) return AnalysisType;
	hasRequiredAnalysisType$1 = 1;
	Object.defineProperty(AnalysisType, "__esModule", { value: true });
	AnalysisType.AnalysisTypeSchema = AnalysisType.AnalysisType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var AnalysisType$1;
	(function (AnalysisType) {
	    AnalysisType["OperatingPoint"] = "OperatingPoint";
	    AnalysisType["Transient"] = "Transient";
	    AnalysisType["Boundary"] = "Boundary";
	    AnalysisType["BusFunction"] = "BusFunction";
	    AnalysisType["PowerTraversal"] = "PowerTraversal";
	    AnalysisType["NetDrive"] = "NetDrive";
	    AnalysisType["NetDriveInfo"] = "NetDriveInfo";
	    AnalysisType["NetDriveScenario"] = "NetDriveScenario";
	    AnalysisType["NetDriveTraversal"] = "NetDriveTraversal";
	    AnalysisType["NetDriveVoltage"] = "NetDriveVoltage";
	    AnalysisType["NetDriveLegacy"] = "NetDriveLegacy";
	    AnalysisType["DriveNetwork"] = "DriveNetwork";
	    AnalysisType["NetAnnotation"] = "NetAnnotation";
	    AnalysisType["NetworkAnalysisSegmentation"] = "NetworkAnalysisSegmentation";
	    AnalysisType["SegmentedNetworkAnalysis"] = "SegmentedNetworkAnalysis";
	    AnalysisType["SpiceNetworkAnalysis"] = "SpiceNetworkAnalysis";
	    AnalysisType["LogicalBus"] = "LogicalBus";
	    AnalysisType["LogicalBusEnumeration"] = "LogicalBusEnumeration";
	    AnalysisType["ComponentFunction"] = "ComponentFunction";
	    AnalysisType["CircuitState"] = "CircuitState";
	    AnalysisType["OperatingState"] = "OperatingState";
	    AnalysisType["SignalNetworkBuild"] = "SignalNetworkBuild";
	    AnalysisType["PowerNetworkBuild"] = "PowerNetworkBuild";
	    AnalysisType["PossibleValuesBoundary"] = "PossibleValuesBoundary";
	    AnalysisType["DriveModeNetDependency"] = "DriveModeNetDependency";
	    AnalysisType["NetBoundaryMap"] = "NetBoundaryMap";
	    AnalysisType["DifferentialPairComponentAnalysis"] = "DifferentialPairComponentAnalysis";
	    AnalysisType["DifferentialPairEnumerationAnalysis"] = "DifferentialPairEnumerationAnalysis";
	    AnalysisType["DifferentialPairValidationAnalysis"] = "DifferentialPairValidationAnalysis";
	})(AnalysisType$1 || (AnalysisType.AnalysisType = AnalysisType$1 = {}));
	AnalysisType.AnalysisTypeSchema = zod_1.default.nativeEnum(AnalysisType$1);
	
	return AnalysisType;
}

var ComponentFunctionAnalysisResult = {};

var IInsightError = {};

var hasRequiredIInsightError;

function requireIInsightError () {
	if (hasRequiredIInsightError) return IInsightError;
	hasRequiredIInsightError = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IInsightErrorSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const _1 = requireDist$1();
		exports.IInsightErrorSchema = zod_1.default.object({
		    id: zod_1.default.string(),
		    errorId: zod_1.default.string(),
		    level: _1.DesignModels.BoundaryLevelSchema,
		    message: zod_1.default.string(),
		    circuitState: zod_1.default.lazy(() => _1.SerializedCircuitStateOrPointerSchema).optional(),
		    circuitObjects: zod_1.default
		        .lazy(() => _1.DesignModels.IBoundaryErrorCircuitObjectSchema)
		        .array()
		        .optional(),
		    innerError: zod_1.default.lazy(() => exports.IInsightErrorSchema).optional(),
		});
		
	} (IInsightError));
	return IInsightError;
}

var hasRequiredComponentFunctionAnalysisResult;

function requireComponentFunctionAnalysisResult () {
	if (hasRequiredComponentFunctionAnalysisResult) return ComponentFunctionAnalysisResult;
	hasRequiredComponentFunctionAnalysisResult = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentFunctionAnalysisResultSerializedSchema = exports.ComponentResourceDataSchema = exports.ComponentFunctionDataSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const AnalysisType_1 = requireAnalysisType$1();
		const IComponentFunction_1 = requireIComponentFunction();
		const IComponentFunctionAssignment_1 = requireIComponentFunctionAssignment();
		const IComponentResource_1 = requireIComponentResource();
		const IInsightError_1 = requireIInsightError();
		exports.ComponentFunctionDataSchema = zod_1.z.object({
		    functionId: zod_1.z.string(),
		    definition: zod_1.z.lazy(() => IComponentFunction_1.IComponentFunctionSchema),
		    assignment: zod_1.z.lazy(() => IComponentFunctionAssignment_1.IComponentFunctionAssignmentSchema).nullable(),
		    isActive: zod_1.z.boolean(),
		    insights: zod_1.z.lazy(() => IInsightError_1.IInsightErrorSchema).array(),
		});
		exports.ComponentResourceDataSchema = zod_1.z.object({
		    resourceId: zod_1.z.string(),
		    definition: zod_1.z.lazy(() => IComponentResource_1.IComponentResourceSchema),
		    consumption: zod_1.z.lazy(() => IComponentResource_1.ComponentResourceFunctionConsumptionSchema).nullable(),
		    insights: zod_1.z.lazy(() => IInsightError_1.IInsightErrorSchema).array(),
		});
		exports.ComponentFunctionAnalysisResultSerializedSchema = zod_1.z.object({
		    type: zod_1.z.literal(AnalysisType_1.AnalysisType.ComponentFunction),
		    result: zod_1.z.object({
		        moduleName: zod_1.z.string(),
		        circuitStateId: zod_1.z.string(),
		        functions: zod_1.z.record(zod_1.z.string(), zod_1.z.lazy(() => exports.ComponentFunctionDataSchema)),
		        resources: zod_1.z.record(zod_1.z.string(), zod_1.z.lazy(() => exports.ComponentResourceDataSchema)),
		    }),
		});
		
	} (ComponentFunctionAnalysisResult));
	return ComponentFunctionAnalysisResult;
}

var LogicalBusAnalysisResult = {};

var hasRequiredLogicalBusAnalysisResult;

function requireLogicalBusAnalysisResult () {
	if (hasRequiredLogicalBusAnalysisResult) return LogicalBusAnalysisResult;
	hasRequiredLogicalBusAnalysisResult = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.LogicalBusEnumerationAnalysisResultSerializedSchema = exports.LogicalBusAnalysisResultSerializedSchema = exports.LogicalBusDataSchema = exports.LogicalBusAssignmentDataSchema = exports.FunctionSignalConnectionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const AnalysisType_1 = requireAnalysisType$1();
		const SignalId_1 = requireSignalId();
		const IComponentFunctionAssignment_1 = requireIComponentFunctionAssignment();
		const IComponentFunctionClassParameters_1 = requireIComponentFunctionClassParameters();
		const IInsightError_1 = requireIInsightError();
		exports.FunctionSignalConnectionSchema = zod_1.z.object({
		    functionSignalName: zod_1.z.string(),
		    componentSignals: zod_1.z.array(zod_1.z.object({
		        signalId: SignalId_1.SignalIdSchema,
		        signalName: zod_1.z.string(),
		        nets: zod_1.z.string().array(),
		    })),
		});
		exports.LogicalBusAssignmentDataSchema = zod_1.z.object({
		    moduleName: zod_1.z.string(),
		    functionName: zod_1.z.string(),
		    functionId: zod_1.z.string(),
		    functionClass: zod_1.z.string(),
		    assignment: zod_1.z.lazy(() => IComponentFunctionAssignment_1.IComponentFunctionAssignmentSchema),
		    signalConnections: zod_1.z.lazy(() => exports.FunctionSignalConnectionSchema).array(),
		    classParameters: zod_1.z.lazy(() => IComponentFunctionClassParameters_1.IComponentFunctionClassParametersSchema).optional(),
		});
		exports.LogicalBusDataSchema = zod_1.z.object({
		    nets: zod_1.z.string().array(),
		    className: zod_1.z.string(),
		    assignments: zod_1.z.lazy(() => exports.LogicalBusAssignmentDataSchema).array(),
		    insights: zod_1.z.lazy(() => IInsightError_1.IInsightErrorSchema).array(),
		});
		exports.LogicalBusAnalysisResultSerializedSchema = zod_1.z.object({
		    type: zod_1.z.literal(AnalysisType_1.AnalysisType.LogicalBus),
		    result: zod_1.z.object({
		        busId: zod_1.z.string(),
		        circuitStateId: zod_1.z.string(),
		        logicalBusData: zod_1.z.lazy(() => exports.LogicalBusDataSchema).array(),
		    }),
		});
		exports.LogicalBusEnumerationAnalysisResultSerializedSchema = zod_1.z.object({
		    type: zod_1.z.literal(AnalysisType_1.AnalysisType.LogicalBusEnumeration),
		    result: zod_1.z.object({
		        circuitStateIds: zod_1.z.string().array(),
		        buses: zod_1.z.array(zod_1.z.object({
		            busId: zod_1.z.string(),
		            circuitStateIds: zod_1.z.string().array(),
		        })),
		    }),
		});
		
	} (LogicalBusAnalysisResult));
	return LogicalBusAnalysisResult;
}

var OrchestratorInspection = {};

var hasRequiredOrchestratorInspection;

function requireOrchestratorInspection () {
	if (hasRequiredOrchestratorInspection) return OrchestratorInspection;
	hasRequiredOrchestratorInspection = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.InspectionClientMessageSchema = exports.InspectionServerMessageSchema = exports.IOrchestratorEventSchema = exports.OrchestratorEventTypeSchema = exports.OrchestratorEventType = exports.IOrchestratorSnapshotSchema = exports.IOrchestratorProfilingSnapshotSchema = exports.IActiveProfileSchema = exports.IProfileFilterSchema = exports.IProfileRequestSchema = exports.InspectionServerStateSchema = exports.InspectionServerState = exports.IProcessResourceStatsSchema = exports.IOrchestratorWorkerPoolStatsSchema = exports.IWorkerMetricsSnapshotSchema = exports.IWorkerMetricSampleSchema = exports.IOrchestratorQueueStatsSchema = exports.IAnalysisNodeSnapshotSchema = exports.INodeLockEntrySchema = exports.NodeLockStateSchema = exports.IAnalysisTimingDataSchema = exports.AnalysisNodeStatusSchema = exports.AnalysisNodeStatus = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const AnalysisType_1 = requireAnalysisType$1();
		var AnalysisNodeStatus;
		(function (AnalysisNodeStatus) {
		    AnalysisNodeStatus["Adding"] = "Adding";
		    AnalysisNodeStatus["Pending"] = "Pending";
		    AnalysisNodeStatus["Running"] = "Running";
		    AnalysisNodeStatus["Yielded"] = "Yielded";
		    AnalysisNodeStatus["Awaiting"] = "Awaiting";
		    AnalysisNodeStatus["Complete"] = "Complete";
		    AnalysisNodeStatus["Failed"] = "Failed";
		})(AnalysisNodeStatus || (exports.AnalysisNodeStatus = AnalysisNodeStatus = {}));
		exports.AnalysisNodeStatusSchema = zod_1.z.nativeEnum(AnalysisNodeStatus);
		exports.IAnalysisTimingDataSchema = zod_1.z.object({
		    addedAt: zod_1.z.number(),
		    startedAt: zod_1.z.number().optional(),
		    endedAt: zod_1.z.number().optional(),
		    activeRunTimeMs: zod_1.z.number().optional(),
		    totalTimeMs: zod_1.z.number().optional(),
		    totalCpuTimeMs: zod_1.z.number().optional(),
		    peakHeapBytes: zod_1.z.number().optional(),
		});
		exports.NodeLockStateSchema = zod_1.z.enum(['requested', 'held', 'released']);
		exports.INodeLockEntrySchema = zod_1.z.object({
		    lockUid: zod_1.z.string(),
		    resourceName: zod_1.z.string(),
		    mode: zod_1.z.enum(['shared', 'exclusive']),
		    state: exports.NodeLockStateSchema,
		    requestedAt: zod_1.z.number(),
		    acquiredAt: zod_1.z.number().optional(),
		    releasedAt: zod_1.z.number().optional(),
		});
		exports.IAnalysisNodeSnapshotSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    type: AnalysisType_1.AnalysisTypeSchema,
		    status: exports.AnalysisNodeStatusSchema,
		    dependencies: zod_1.z.array(zod_1.z.string()),
		    dependants: zod_1.z.array(zod_1.z.string()),
		    timing: exports.IAnalysisTimingDataSchema,
		    workerId: zod_1.z.number().optional(),
		    workerName: zod_1.z.string().optional(),
		    yieldCount: zod_1.z.number(),
		    locks: zod_1.z.array(exports.INodeLockEntrySchema).optional(),
		});
		exports.IOrchestratorQueueStatsSchema = zod_1.z.object({
		    concurrency: zod_1.z.number(),
		    pending: zod_1.z.number(),
		    running: zod_1.z.number(),
		});
		exports.IWorkerMetricSampleSchema = zod_1.z.object({
		    timestamp: zod_1.z.number(),
		    value: zod_1.z.number(),
		});
		exports.IWorkerMetricsSnapshotSchema = zod_1.z.object({
		    workerId: zod_1.z.number(),
		    workerName: zod_1.z.string().optional(),
		    cpuPercent: zod_1.z.number(),
		    heapUsedBytes: zod_1.z.number().optional(),
		    rssBytes: zod_1.z.number().optional(),
		    lastMemUpdateAt: zod_1.z.number().optional(),
		    activeJobs: zod_1.z.number(),
		    cpuHistory: zod_1.z.array(exports.IWorkerMetricSampleSchema),
		    heapHistory: zod_1.z.array(exports.IWorkerMetricSampleSchema),
		});
		exports.IOrchestratorWorkerPoolStatsSchema = zod_1.z.object({
		    size: zod_1.z.number(),
		    activeJobs: zod_1.z.number(),
		    workers: zod_1.z.array(exports.IWorkerMetricsSnapshotSchema).optional(),
		});
		exports.IProcessResourceStatsSchema = zod_1.z.object({
		    pid: zod_1.z.number(),
		    cpuPercent: zod_1.z.number(),
		    rssBytes: zod_1.z.number(),
		    heapUsedBytes: zod_1.z.number(),
		    heapTotalBytes: zod_1.z.number(),
		});
		var InspectionServerState;
		(function (InspectionServerState) {
		    InspectionServerState["Initializing"] = "initializing";
		    InspectionServerState["Processing"] = "processing";
		    InspectionServerState["Idle"] = "idle";
		})(InspectionServerState || (exports.InspectionServerState = InspectionServerState = {}));
		exports.InspectionServerStateSchema = zod_1.z.nativeEnum(InspectionServerState);
		exports.IProfileRequestSchema = zod_1.z.object({
		    cpu: zod_1.z.boolean(),
		    heapSampling: zod_1.z.boolean(),
		    heapSnapshot: zod_1.z.boolean(),
		});
		exports.IProfileFilterSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    regexSource: zod_1.z.string(),
		    regexFlags: zod_1.z.string(),
		    types: exports.IProfileRequestSchema,
		});
		exports.IActiveProfileSchema = zod_1.z.object({
		    nodeId: zod_1.z.string(),
		    workerId: zod_1.z.number(),
		    pumpIndex: zod_1.z.number(),
		    types: exports.IProfileRequestSchema,
		    startedAt: zod_1.z.number(),
		});
		exports.IOrchestratorProfilingSnapshotSchema = zod_1.z.object({
		    filters: zod_1.z.array(exports.IProfileFilterSchema),
		    activeProfiles: zod_1.z.array(exports.IActiveProfileSchema),
		});
		exports.IOrchestratorSnapshotSchema = zod_1.z.object({
		    seq: zod_1.z.number(),
		    timestamp: zod_1.z.number(),
		    state: exports.InspectionServerStateSchema,
		    nodes: zod_1.z.array(exports.IAnalysisNodeSnapshotSchema),
		    queue: exports.IOrchestratorQueueStatsSchema,
		    workerPool: exports.IOrchestratorWorkerPoolStatsSchema.optional(),
		    resources: exports.IProcessResourceStatsSchema.optional(),
		    profiling: exports.IOrchestratorProfilingSnapshotSchema.optional(),
		});
		var OrchestratorEventType;
		(function (OrchestratorEventType) {
		    OrchestratorEventType["NodeAdded"] = "node:added";
		    OrchestratorEventType["NodeStatusChanged"] = "node:status_changed";
		    OrchestratorEventType["NodeDependencyAdded"] = "node:dependency_added";
		    OrchestratorEventType["NodeCompleted"] = "node:completed";
		    OrchestratorEventType["NodeFailed"] = "node:failed";
		    OrchestratorEventType["NodeYielded"] = "node:yielded";
		    OrchestratorEventType["WorkerAssigned"] = "node:worker_assigned";
		    OrchestratorEventType["LockRequested"] = "node:lock_requested";
		    OrchestratorEventType["LockAcquired"] = "node:lock_acquired";
		    OrchestratorEventType["LockReleased"] = "node:lock_released";
		    OrchestratorEventType["ProfileStarted"] = "profile:started";
		    OrchestratorEventType["ProfileCaptured"] = "profile:captured";
		    OrchestratorEventType["ProfileWritten"] = "profile:written";
		    OrchestratorEventType["ProfileWriteFailed"] = "profile:write_failed";
		    OrchestratorEventType["ProfileSkipped"] = "profile:skipped";
		    OrchestratorEventType["ProfileFilterAdded"] = "profile:filter_added";
		    OrchestratorEventType["ProfileFilterRemoved"] = "profile:filter_removed";
		    OrchestratorEventType["CapacityChanged"] = "pool:capacity_changed";
		})(OrchestratorEventType || (exports.OrchestratorEventType = OrchestratorEventType = {}));
		exports.OrchestratorEventTypeSchema = zod_1.z.nativeEnum(OrchestratorEventType);
		exports.IOrchestratorEventSchema = zod_1.z.object({
		    type: exports.OrchestratorEventTypeSchema,
		    seq: zod_1.z.number(),
		    timestamp: zod_1.z.number(),
		    nodeId: zod_1.z.string(),
		    data: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
		});
		exports.InspectionServerMessageSchema = zod_1.z.discriminatedUnion('type', [
		    zod_1.z.object({ type: zod_1.z.literal('snapshot'), data: exports.IOrchestratorSnapshotSchema }),
		    zod_1.z.object({ type: zod_1.z.literal('event'), data: exports.IOrchestratorEventSchema }),
		    zod_1.z.object({ type: zod_1.z.literal('query_result'), requestId: zod_1.z.string(), data: zod_1.z.unknown().optional() }),
		]);
		exports.InspectionClientMessageSchema = zod_1.z.discriminatedUnion('type', [
		    zod_1.z.object({ type: zod_1.z.literal('request_snapshot') }),
		    zod_1.z.object({ type: zod_1.z.literal('query_node'), requestId: zod_1.z.string(), nodeId: zod_1.z.string() }),
		    zod_1.z.object({
		        type: zod_1.z.literal('query_completed'),
		        requestId: zod_1.z.string(),
		        sortBy: zod_1.z.enum(['duration', 'endedAt']).optional(),
		        limit: zod_1.z.number().optional(),
		    }),
		    zod_1.z.object({ type: zod_1.z.literal('query_waiting_on'), requestId: zod_1.z.string(), nodeId: zod_1.z.string() }),
		    zod_1.z.object({
		        type: zod_1.z.literal('add_profile_filter'),
		        requestId: zod_1.z.string(),
		        regexSource: zod_1.z.string(),
		        regexFlags: zod_1.z.string().optional(),
		        types: exports.IProfileRequestSchema,
		    }),
		    zod_1.z.object({ type: zod_1.z.literal('remove_profile_filter'), requestId: zod_1.z.string(), filterId: zod_1.z.string() }),
		    zod_1.z.object({ type: zod_1.z.literal('list_profile_filters'), requestId: zod_1.z.string() }),
		]);
		
	} (OrchestratorInspection));
	return OrchestratorInspection;
}

var hasRequiredAnalysisRequirements;

function requireAnalysisRequirements () {
	if (hasRequiredAnalysisRequirements) return AnalysisRequirements;
	hasRequiredAnalysisRequirements = 1;
	(function (exports) {
		var __createBinding = (AnalysisRequirements && AnalysisRequirements.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (AnalysisRequirements && AnalysisRequirements.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAnalysisType$1(), exports);
		__exportStar(requireComponentFunctionAnalysisResult(), exports);
		__exportStar(requireLogicalBusAnalysisResult(), exports);
		__exportStar(requireOrchestratorInspection(), exports);
		
	} (AnalysisRequirements));
	return AnalysisRequirements;
}

var MappedBoundaries = {};

var BoundaryMapperUtil = {};

var hasRequiredBoundaryMapperUtil;

function requireBoundaryMapperUtil () {
	if (hasRequiredBoundaryMapperUtil) return BoundaryMapperUtil;
	hasRequiredBoundaryMapperUtil = 1;
	Object.defineProperty(BoundaryMapperUtil, "__esModule", { value: true });
	BoundaryMapperUtil.BoundaryMapperUtil = BoundaryMapperUtil.BoundaryMapperUtilMergeMode = void 0;
	const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
	const BoundaryClass_1 = requireBoundaryClass();
	const BoundaryLevel_1 = requireBoundaryLevel();
	const BoundaryTarget_1 = requireBoundaryTarget();
	const BoundaryAssertionType_1 = requireBoundaryAssertionType();
	const INumericalBoundaryDefinition_1 = requireINumericalBoundaryDefinition();
	const Utils_1 = requireUtils$4();
	var BoundaryMapperUtilMergeMode;
	(function (BoundaryMapperUtilMergeMode) {
	    BoundaryMapperUtilMergeMode[BoundaryMapperUtilMergeMode["Merge"] = 0] = "Merge";
	    BoundaryMapperUtilMergeMode[BoundaryMapperUtilMergeMode["Replace"] = 1] = "Replace";
	})(BoundaryMapperUtilMergeMode || (BoundaryMapperUtil.BoundaryMapperUtilMergeMode = BoundaryMapperUtilMergeMode = {}));
	class BoundaryMapperUtil$1 {
	    static getAmrBoundaries(signals, boundaries) {
	        return Array.from(this.getAmrBoundaryEntries(signals, boundaries).values()).flat();
	    }
	    static getAmrBoundaryEntries(signals, boundaries) {
	        return new Map(boundaries.map((b) => [b, this.toHilAmrBoundary(signals, b)]).filter(([, b]) => !!b?.length));
	    }
	    static mergeAmrBoundaries(signals, originalBoundaries, newBoundaries, mode) {
	        if (mode === BoundaryMapperUtilMergeMode.Replace) {
	            originalBoundaries = this.withoutAmrBoundaries(signals, originalBoundaries);
	        }
	        return originalBoundaries.concat(newBoundaries.flatMap((b) => this.mintAmrBoundaries(b)));
	    }
	    static mintAmrBoundaries(boundary) {
	        return this.fromHilAmrBoundary(boundary);
	    }
	    static withoutAmrBoundaries(signals, boundaries) {
	        const amrEntries = this.getAmrBoundaryEntries(signals, boundaries);
	        return boundaries.filter((b) => !amrEntries.has(b));
	    }
	    static toHilAmrBoundary(signals, boundary) {
	        if (!(0, INumericalBoundaryDefinition_1.isBoundaryNumericalDefinition)(boundary.boundary)) {
	            return null;
	        }
	        const numerical = boundary.boundary.numerical;
	        if (boundary.boundary.class !== BoundaryClass_1.BoundaryClass.IC_AbsoluteMaximumRating) {
	            return null;
	        }
	        if (!(0, BoundaryTarget_1.isBoundaryTargetRelativeSignals)(boundary.boundary.target)) {
	            return null;
	        }
	        return (0, Utils_1.toArray)(boundary.boundary.target.signal).map((signalId) => {
	            const relativeSignal = (0, BoundaryTarget_1.getBoundaryTargetSignalIds)(signals, boundary.boundary.relative_to)[0] ?? null;
	            switch (numerical.type) {
	                case 'minimum': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: numerical.value?.toString(),
	                        minimumRelativeToSignalId: relativeSignal,
	                        maximum: null,
	                        maximumRelativeToSignalId: null,
	                    };
	                }
	                case 'maximum': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: null,
	                        minimumRelativeToSignalId: null,
	                        maximum: numerical.value?.toString(),
	                        maximumRelativeToSignalId: relativeSignal,
	                    };
	                }
	                case 'range': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: numerical.minVal?.toString(),
	                        minimumRelativeToSignalId: relativeSignal,
	                        maximum: numerical.maxVal?.toString(),
	                        maximumRelativeToSignalId: relativeSignal,
	                    };
	                }
	                case 'absolute': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: null,
	                        minimumRelativeToSignalId: null,
	                        maximum: null,
	                        maximumRelativeToSignalId: null,
	                    };
	                }
	            }
	        });
	    }
	    static fromHilAmrBoundary(boundary) {
	        const numerical = this.buildNumericalBoundary(boundary);
	        return Array.from(numerical.entries()).flatMap(([relativeSignalId, numerical]) => {
	            if (!numerical) {
	                return [];
	            }
	            return {
	                id: 'amr_' + (0, Utils_1.randomAlphaStr)(16),
	                global: true,
	                boundary: {
	                    type: BoundaryAssertionType_1.BoundaryAssertionType.Numerical,
	                    level: BoundaryLevel_1.BoundaryLevel.Error,
	                    class: BoundaryClass_1.BoundaryClass.IC_AbsoluteMaximumRating,
	                    target: {
	                        signal: boundary.signalId,
	                    },
	                    relative_to: typeof relativeSignalId !== undefined ? { signal: relativeSignalId } : undefined,
	                    numerical,
	                },
	            };
	        });
	    }
	    static safeParseNumericalSIValue(value, characteristic) {
	        try {
	            const unit = BoundaryCharacteristic_1.BoundaryCharacteristicSIDefinitions[characteristic];
	            const result = (0, Utils_1.parseNumericalSIValue)(value, unit);
	            return result ?? parseFloat(value);
	        }
	        catch (error) {
	            console.warn(`Failed to parse boundary value "${value}" for ${characteristic}:`, error instanceof Error ? error.message : error);
	            return parseFloat(value);
	        }
	    }
	    static buildNumericalBoundary(boundary) {
	        const relativeSignals = new Set([
	            boundary.minimumRelativeToSignalId,
	            boundary.maximumRelativeToSignalId,
	            boundary.nominalRelativeToSignalId,
	        ].filter((s) => typeof s === 'string' || typeof s === 'number'));
	        return new Map(Array.from(relativeSignals).map((signalId) => {
	            const base = {
	                characteristic: boundary.characteristic,
	                nominalValue: boundary.nominal ? this.safeParseNumericalSIValue(boundary.nominal, boundary.characteristic) : undefined,
	            };
	            if (typeof boundary.minimum === 'string' &&
	                boundary.minimum !== '' &&
	                boundary.minimumRelativeToSignalId === signalId &&
	                typeof boundary.maximum === 'string' &&
	                boundary.maximum !== '' &&
	                boundary.maximumRelativeToSignalId === signalId) {
	                return [
	                    signalId,
	                    {
	                        type: 'range',
	                        minVal: this.safeParseNumericalSIValue(boundary.minimum, boundary.characteristic),
	                        maxVal: this.safeParseNumericalSIValue(boundary.maximum, boundary.characteristic),
	                        ...base,
	                    },
	                ];
	            }
	            if (typeof boundary.minimum === 'string' &&
	                boundary.minimum !== '' &&
	                boundary.minimumRelativeToSignalId === signalId) {
	                return [
	                    signalId,
	                    {
	                        type: 'minimum',
	                        value: this.safeParseNumericalSIValue(boundary.minimum, boundary.characteristic),
	                        ...base,
	                    },
	                ];
	            }
	            if (typeof boundary.maximum === 'string' &&
	                boundary.maximum !== '' &&
	                boundary.maximumRelativeToSignalId === signalId) {
	                return [
	                    signalId,
	                    {
	                        type: 'maximum',
	                        value: this.safeParseNumericalSIValue(boundary.maximum, boundary.characteristic),
	                        ...base,
	                    },
	                ];
	            }
	            if (typeof boundary.nominal === 'string' &&
	                boundary.nominal !== '' &&
	                boundary.nominalRelativeToSignalId === signalId) {
	                return [
	                    signalId,
	                    {
	                        type: 'absolute',
	                        value: this.safeParseNumericalSIValue(boundary.nominal, boundary.characteristic),
	                        ...base,
	                    },
	                ];
	            }
	            return [signalId, null];
	        }));
	    }
	    static getOperatingConditionBoundaries(signals, boundaries) {
	        return Array.from(this.getOperatingConditionBoundaryEntries(signals, boundaries).values()).flat();
	    }
	    static getOperatingConditionBoundaryEntries(signals, boundaries) {
	        return new Map(boundaries
	            .map((b) => [b, this.toHilOperatingConditionBoundary(signals, b)])
	            .filter(([, b]) => !!b));
	    }
	    static toHilOperatingConditionBoundary(signals, boundary) {
	        if (!(0, INumericalBoundaryDefinition_1.isBoundaryNumericalDefinition)(boundary.boundary)) {
	            return null;
	        }
	        const numerical = boundary.boundary.numerical;
	        if (boundary.boundary.class !== BoundaryClass_1.BoundaryClass.IC_ElectricalCharacteristics) {
	            return null;
	        }
	        if (!(0, BoundaryTarget_1.isBoundaryTargetRelativeSignals)(boundary.boundary.target)) {
	            return null;
	        }
	        return (0, Utils_1.toArray)(boundary.boundary.target.signal).map((signalId) => {
	            const relativeSignal = (0, BoundaryTarget_1.getBoundaryTargetSignalIds)(signals, boundary.boundary.relative_to)[0] ?? null;
	            switch (numerical.type) {
	                case 'minimum': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: numerical.value?.toString(),
	                        minimumRelativeToSignalId: relativeSignal,
	                        nominal: numerical.nominalValue?.toString() ?? null,
	                        nominalRelativeToSignalId: relativeSignal,
	                        maximum: null,
	                        maximumRelativeToSignalId: null,
	                    };
	                }
	                case 'maximum': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: null,
	                        minimumRelativeToSignalId: null,
	                        nominal: numerical.nominalValue?.toString() ?? null,
	                        nominalRelativeToSignalId: relativeSignal,
	                        maximum: numerical.value?.toString(),
	                        maximumRelativeToSignalId: relativeSignal,
	                    };
	                }
	                case 'range': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: numerical.minVal?.toString(),
	                        minimumRelativeToSignalId: relativeSignal,
	                        nominal: numerical.nominalValue?.toString() ?? null,
	                        nominalRelativeToSignalId: relativeSignal,
	                        maximum: numerical.maxVal?.toString(),
	                        maximumRelativeToSignalId: relativeSignal,
	                    };
	                }
	                case 'absolute': {
	                    return {
	                        signalId,
	                        characteristic: numerical.characteristic,
	                        minimum: null,
	                        minimumRelativeToSignalId: null,
	                        nominal: numerical.value?.toString(),
	                        nominalRelativeToSignalId: relativeSignal,
	                        maximum: null,
	                        maximumRelativeToSignalId: null,
	                    };
	                }
	            }
	        });
	    }
	    static fromHilOperatingConditionBoundary(boundary, modes) {
	        const numerical = this.buildNumericalBoundary(boundary);
	        let conditions = {
	            global: true,
	        };
	        if (modes.length > 0) {
	            conditions = {
	                conditions: modes.map((m) => ({
	                    mode: m.id,
	                    mode_state: true,
	                })),
	            };
	        }
	        return Array.from(numerical.entries()).flatMap(([relativeSignalId, numerical]) => {
	            if (!numerical) {
	                return [];
	            }
	            return {
	                ...conditions,
	                id: 'iec_' + (0, Utils_1.randomAlphaStr)(16),
	                boundary: {
	                    type: BoundaryAssertionType_1.BoundaryAssertionType.Numerical,
	                    level: BoundaryLevel_1.BoundaryLevel.Warning,
	                    class: BoundaryClass_1.BoundaryClass.IC_ElectricalCharacteristics,
	                    target: {
	                        signal: boundary.signalId,
	                    },
	                    relative_to: typeof relativeSignalId !== undefined ? { signal: relativeSignalId } : undefined,
	                    numerical,
	                },
	            };
	        });
	    }
	    static mergeOperatingConditionBoundaries(signals, originalBoundaries, newBoundaries, modes, mode) {
	        if (mode === BoundaryMapperUtilMergeMode.Replace) {
	            originalBoundaries = this.withoutOperatingConditionBoundaries(signals, originalBoundaries);
	        }
	        return originalBoundaries.concat(newBoundaries.flatMap((b) => this.mintOperatingConditionBoundaries(b, modes)));
	    }
	    static mintOperatingConditionBoundaries(boundary, modes) {
	        return this.fromHilOperatingConditionBoundary(boundary, modes);
	    }
	    static withoutOperatingConditionBoundaries(signals, boundaries) {
	        const ocEntries = this.getOperatingConditionBoundaryEntries(signals, boundaries);
	        return boundaries.filter((b) => !ocEntries.has(b));
	    }
	}
	BoundaryMapperUtil.BoundaryMapperUtil = BoundaryMapperUtil$1;
	
	return BoundaryMapperUtil;
}

var HilAbsoluteMaximumRatingBoundary = {};

var hasRequiredHilAbsoluteMaximumRatingBoundary;

function requireHilAbsoluteMaximumRatingBoundary () {
	if (hasRequiredHilAbsoluteMaximumRatingBoundary) return HilAbsoluteMaximumRatingBoundary;
	hasRequiredHilAbsoluteMaximumRatingBoundary = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.HilAbsoluteMaximumRatingBoundarySchema = void 0;
		exports.extendAbsoluteMaximumRatingSchemaWithSignalIds = extendAbsoluteMaximumRatingSchemaWithSignalIds;
		const z = /*@__PURE__*/ requireZod();
		const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
		exports.HilAbsoluteMaximumRatingBoundarySchema = z.object({
		    signalId: z.union([z.string(), z.number()]).describe('The signal ID that the absolute maximum rating applies to.'),
		    characteristic: z.nativeEnum(BoundaryCharacteristic_1.BoundaryCharacteristic),
		    minimum: z
		        .string()
		        .nullable()
		        .describe('The minimum value of the characteristic in the appropriate SI units for the characteristic.'),
		    minimumRelativeToSignalId: z
		        .union([z.string(), z.number()])
		        .nullable()
		        .describe('The signal that the minimum value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
		    maximum: z
		        .string()
		        .nullable()
		        .describe('The maximum value of the characteristic in the appropriate SI units for the characteristic.'),
		    maximumRelativeToSignalId: z
		        .union([z.string(), z.number()])
		        .nullable()
		        .describe('The signal that the maximum value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
		});
		function extendAbsoluteMaximumRatingSchemaWithSignalIds(signalIds, allowedCharacteristics) {
		    const ratedSchema = exports.HilAbsoluteMaximumRatingBoundarySchema.extend({
		        characteristic: allowedCharacteristics ? z.enum(allowedCharacteristics) : BoundaryCharacteristic_1.BoundaryCharacteristicSchema,
		    });
		    if (signalIds.length < 1) {
		        return ratedSchema;
		    }
		    const useSignalIds = ['', ...signalIds.map((id) => id.toString())];
		    return ratedSchema.extend({
		        signalId: z.enum(useSignalIds).describe(exports.HilAbsoluteMaximumRatingBoundarySchema.shape.signalId.description),
		        minimumRelativeToSignalId: z
		            .enum(useSignalIds)
		            .nullable()
		            .describe(exports.HilAbsoluteMaximumRatingBoundarySchema.shape.minimumRelativeToSignalId.description),
		        maximumRelativeToSignalId: z
		            .enum(useSignalIds)
		            .nullable()
		            .describe(exports.HilAbsoluteMaximumRatingBoundarySchema.shape.maximumRelativeToSignalId.description),
		    });
		}
		
	} (HilAbsoluteMaximumRatingBoundary));
	return HilAbsoluteMaximumRatingBoundary;
}

var HilOperatingConditionBoundary = {};

var hasRequiredHilOperatingConditionBoundary;

function requireHilOperatingConditionBoundary () {
	if (hasRequiredHilOperatingConditionBoundary) return HilOperatingConditionBoundary;
	hasRequiredHilOperatingConditionBoundary = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.HilOperatingConditionBoundarySchema = exports.OPERATING_CONDITION_BOUNDARY_DESCRIPTION = void 0;
		exports.extendOperatingConditionSchemaWithSignalIds = extendOperatingConditionSchemaWithSignalIds;
		const z = /*@__PURE__*/ requireZod();
		const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
		const HilAbsoluteMaximumRatingBoundary_1 = requireHilAbsoluteMaximumRatingBoundary();
		exports.OPERATING_CONDITION_BOUNDARY_DESCRIPTION = 'Defines the operating conditions or electrical characteristics for a signal. These conditions describe the correct operating mode of the device where it is intended to be used.';
		exports.HilOperatingConditionBoundarySchema = HilAbsoluteMaximumRatingBoundary_1.HilAbsoluteMaximumRatingBoundarySchema.extend({
		    nominal: z
		        .string()
		        .nullable()
		        .describe('The nominal value of the characteristic in the appropriate SI units for the characteristic.'),
		    nominalRelativeToSignalId: z
		        .union([z.string(), z.number()])
		        .nullable()
		        .describe('The signal that the nominal value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
		}).describe(exports.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
		function extendOperatingConditionSchemaWithSignalIds(signalIds, allowedCharacteristics) {
		    const ratedSchema = exports.HilOperatingConditionBoundarySchema.extend({
		        characteristic: allowedCharacteristics ? z.enum(allowedCharacteristics) : BoundaryCharacteristic_1.BoundaryCharacteristicSchema,
		    }).describe(exports.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
		    if (signalIds.length < 1) {
		        return ratedSchema;
		    }
		    const useSignalIds = ['', ...signalIds.map((id) => id.toString())];
		    return ratedSchema
		        .extend({
		        signalId: z.enum(useSignalIds).describe(exports.HilOperatingConditionBoundarySchema.shape.signalId.description),
		        minimumRelativeToSignalId: z
		            .enum(useSignalIds)
		            .nullable()
		            .describe(exports.HilOperatingConditionBoundarySchema.shape.minimumRelativeToSignalId.description),
		        nominalRelativeToSignalId: z
		            .enum(useSignalIds)
		            .nullable()
		            .describe(exports.HilOperatingConditionBoundarySchema.shape.nominalRelativeToSignalId.description),
		        maximumRelativeToSignalId: z
		            .enum(useSignalIds)
		            .nullable()
		            .describe(exports.HilOperatingConditionBoundarySchema.shape.maximumRelativeToSignalId.description),
		    })
		        .describe(exports.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
		}
		
	} (HilOperatingConditionBoundary));
	return HilOperatingConditionBoundary;
}

var hasRequiredMappedBoundaries;

function requireMappedBoundaries () {
	if (hasRequiredMappedBoundaries) return MappedBoundaries;
	hasRequiredMappedBoundaries = 1;
	(function (exports) {
		var __createBinding = (MappedBoundaries && MappedBoundaries.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (MappedBoundaries && MappedBoundaries.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBoundaryMapperUtil(), exports);
		__exportStar(requireHilAbsoluteMaximumRatingBoundary(), exports);
		__exportStar(requireHilOperatingConditionBoundary(), exports);
		
	} (MappedBoundaries));
	return MappedBoundaries;
}

var CharacteristicExpressionParser = {};

var cjs = {};

var mainAny = {};

var configReadonly = {};

var interopRequireDefault = {exports: {}};

interopRequireDefault.exports;

var hasRequiredInteropRequireDefault;

function requireInteropRequireDefault () {
	if (hasRequiredInteropRequireDefault) return interopRequireDefault.exports;
	hasRequiredInteropRequireDefault = 1;
	(function (module) {
		function _interopRequireDefault(e) {
		  return e && e.__esModule ? e : {
		    "default": e
		  };
		}
		module.exports = _interopRequireDefault, module.exports.__esModule = true, module.exports["default"] = module.exports; 
	} (interopRequireDefault));
	return interopRequireDefault.exports;
}

var _extends = {exports: {}};

_extends.exports;

var hasRequired_extends;

function require_extends () {
	if (hasRequired_extends) return _extends.exports;
	hasRequired_extends = 1;
	(function (module) {
		function _extends() {
		  return module.exports = _extends = Object.assign ? Object.assign.bind() : function (n) {
		    for (var e = 1; e < arguments.length; e++) {
		      var t = arguments[e];
		      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
		    }
		    return n;
		  }, module.exports.__esModule = true, module.exports["default"] = module.exports, _extends.apply(null, arguments);
		}
		module.exports = _extends, module.exports.__esModule = true, module.exports["default"] = module.exports; 
	} (_extends));
	return _extends.exports;
}

var config$3 = {};

var hasRequiredConfig$2;

function requireConfig$2 () {
	if (hasRequiredConfig$2) return config$3;
	hasRequiredConfig$2 = 1;

	Object.defineProperty(config$3, "__esModule", {
	  value: true
	});
	config$3.DEFAULT_CONFIG = void 0;
	config$3.DEFAULT_CONFIG = {
	  // minimum relative difference between two compared values,
	  // used by all comparison functions
	  relTol: 1e-12,
	  // minimum absolute difference between two compared values,
	  // used by all comparison functions
	  absTol: 1e-15,
	  // type of default matrix output. Choose 'matrix' (default) or 'array'
	  matrix: 'Matrix',
	  // type of default number output. Choose 'number' (default) 'BigNumber', 'bigint', or 'Fraction'
	  number: 'number',
	  // type of fallback used for config { number: 'bigint' } when a value cannot be represented
	  // in the configured numeric type. Choose 'number' (default) or 'BigNumber'.
	  numberFallback: 'number',
	  // number of significant digits in BigNumbers
	  precision: 64,
	  // predictable output type of functions. When true, output type depends only
	  // on the input types. When false (default), output type can vary depending
	  // on input values. For example `math.sqrt(-4)` returns `complex('2i')` when
	  // predictable is false, and returns `NaN` when true.
	  predictable: false,
	  // random seed for seeded pseudo random number generation
	  // null = randomly seed
	  randomSeed: null,
	  // legacy behavior for matrix subset. When true, the subset function
	  // returns a matrix or array with the same size as the index (except for scalars).
	  // When false, it returns a matrix or array with a size depending on the type of index.
	  legacySubset: false
	};
	return config$3;
}

var config$2 = {};

var object$3 = {};

var is = {};

var map$2 = {};

var customs = {};

var hasRequiredCustoms;

function requireCustoms () {
	if (hasRequiredCustoms) return customs;
	hasRequiredCustoms = 1;

	Object.defineProperty(customs, "__esModule", {
	  value: true
	});
	customs.getSafeMethod = getSafeMethod;
	customs.getSafeProperty = getSafeProperty;
	customs.isPlainObject = isPlainObject;
	customs.isSafeArrayProperty = isSafeArrayProperty;
	customs.isSafeMethod = isSafeMethod;
	customs.isSafeObjectProperty = isSafeObjectProperty;
	customs.setSafeProperty = setSafeProperty;
	var _object = requireObject$3();
	/**
	 * Get a property of a plain object or array
	 * Throws an error in case the object is not a plain object or the
	 * property is not defined on the object itself
	 * @param {Object} object
	 * @param {string} prop
	 * @return {*} Returns the property value when safe
	 */
	function getSafeProperty(object, prop) {
	  if (isSafeObjectProperty(object, prop) || isSafeArrayProperty(object, prop)) {
	    return object[prop];
	  }
	  if (isSafeMethod(object, prop)) {
	    throw new Error(`Cannot access method "${prop}" as a property`);
	  }
	  if (object === null || object === undefined) {
	    throw new TypeError(`Cannot access property "${prop}": object is ${object}`);
	  }
	  throw new Error('No access to property "' + prop + '"');
	}

	/**
	 * Set a property on a plain object or array.
	 * Throws an error in case the object is not a plain object or the
	 * property would override an inherited property like .constructor or .toString
	 * @param {Object} object
	 * @param {string} prop
	 * @param {*} value
	 * @return {*} Returns the value
	 */
	function setSafeProperty(object, prop, value) {
	  if (isSafeObjectProperty(object, prop) || isSafeArrayProperty(object, prop)) {
	    object[prop] = value;
	    return value;
	  }
	  throw new Error(`No access to property "${prop}"`);
	}

	/**
	 * Test whether a property is safe for reading and writing on an object
	 * For example .constructor and .__proto__ are not safe
	 * @param {Object} object
	 * @param {string} prop
	 * @return {boolean} Returns true when safe
	 */
	function isSafeObjectProperty(object, prop) {
	  if (!isPlainObject(object)) {
	    return false;
	  }
	  return !(prop in Object.prototype);
	}

	/**
	 * Test whether a property is safe for reading and writing on an Array
	 * For example .__proto__ and .constructor are not safe
	 * @param {unknown} array
	 * @param {string | number} prop
	 * @return {boolean} Returns true when safe
	 */
	function isSafeArrayProperty(array, prop) {
	  if (!Array.isArray(array)) {
	    return false;
	  }
	  return typeof prop === 'number' || typeof prop === 'string' && isInteger(prop) || prop === 'length';
	}
	function isInteger(prop) {
	  return /^\d+$/.test(prop);
	}

	/**
	 * Validate whether a method is safe.
	 * Throws an error when that's not the case.
	 * @param {Object} object
	 * @param {string} method
	 * @return {function} Returns the method when valid
	 */
	function getSafeMethod(object, method) {
	  if (!isSafeMethod(object, method)) {
	    throw new Error('No access to method "' + method + '"');
	  }
	  return object[method];
	}

	/**
	 * Check whether a method is safe.
	 * Throws an error when that's not the case (for example for `constructor`).
	 * @param {Object} object
	 * @param {string} method
	 * @return {boolean} Returns true when safe, false otherwise
	 */
	function isSafeMethod(object, method) {
	  if (object === null || object === undefined || typeof object[method] !== 'function') {
	    return false;
	  }

	  // UNSAFE: ghosted
	  // e.g overridden toString
	  // Note that IE10 doesn't support __proto__ and we can't do this check there.
	  if ((0, _object.hasOwnProperty)(object, method) && Object.getPrototypeOf && method in Object.getPrototypeOf(object)) {
	    return false;
	  }

	  // SAFE: whitelisted
	  // e.g toString
	  if (safeNativeMethods.has(method)) {
	    return true;
	  }

	  // UNSAFE: inherited from Object prototype
	  // e.g constructor
	  if (method in Object.prototype) {
	    // 'in' is used instead of hasOwnProperty for nodejs v0.10
	    // which is inconsistent on root prototypes. It is safe
	    // here because Object.prototype is a root object
	    return false;
	  }

	  // UNSAFE: inherited from Function prototype
	  // e.g call, apply
	  if (method in Function.prototype) {
	    // 'in' is used instead of hasOwnProperty for nodejs v0.10
	    // which is inconsistent on root prototypes. It is safe
	    // here because Function.prototype is a root object
	    return false;
	  }
	  return true;
	}
	function isPlainObject(object) {
	  return typeof object === 'object' && object && object.constructor === Object;
	}
	const safeNativeMethods = new Set(['toString', 'valueOf', 'toLocaleString']);
	return customs;
}

var hasRequiredMap$2;

function requireMap$2 () {
	if (hasRequiredMap$2) return map$2;
	hasRequiredMap$2 = 1;

	Object.defineProperty(map$2, "__esModule", {
	  value: true
	});
	map$2.PartitionedMap = map$2.ObjectWrappingMap = void 0;
	map$2.assign = assign;
	map$2.createEmptyMap = createEmptyMap;
	map$2.createMap = createMap;
	map$2.toObject = toObject;
	var _customs = requireCustoms();
	var _is = requireIs();
	/**
	 * A map facade on a bare object.
	 *
	 * The small number of methods needed to implement a scope,
	 * forwarding on to the SafeProperty functions. Over time, the codebase
	 * will stop using this method, as all objects will be Maps, rather than
	 * more security prone objects.
	 */
	class ObjectWrappingMap {
	  constructor(object) {
	    this.wrappedObject = object;
	    this[Symbol.iterator] = this.entries;
	  }
	  keys() {
	    return Object.keys(this.wrappedObject).filter(key => this.has(key)).values();
	  }
	  get(key) {
	    return (0, _customs.getSafeProperty)(this.wrappedObject, key);
	  }
	  set(key, value) {
	    (0, _customs.setSafeProperty)(this.wrappedObject, key, value);
	    return this;
	  }
	  has(key) {
	    return (0, _customs.isSafeObjectProperty)(this.wrappedObject, key) && key in this.wrappedObject;
	  }
	  entries() {
	    return mapIterator(this.keys(), key => [key, this.get(key)]);
	  }
	  forEach(callback) {
	    for (const key of this.keys()) {
	      callback(this.get(key), key, this);
	    }
	  }
	  delete(key) {
	    if ((0, _customs.isSafeObjectProperty)(this.wrappedObject, key)) {
	      delete this.wrappedObject[key];
	    }
	  }
	  clear() {
	    for (const key of this.keys()) {
	      this.delete(key);
	    }
	  }
	  get size() {
	    return Object.keys(this.wrappedObject).length;
	  }
	}

	/**
	 * Create a map with two partitions: a and b.
	 * The set with bKeys determines which keys/values are read/written to map b,
	 * all other values are read/written to map a
	 *
	 * For example:
	 *
	 *   const a = new Map()
	 *   const b = new Map()
	 *   const p = new PartitionedMap(a, b, new Set(['x', 'y']))
	 *
	 * In this case, values `x` and `y` are read/written to map `b`,
	 * all other values are read/written to map `a`.
	 */
	map$2.ObjectWrappingMap = ObjectWrappingMap;
	class PartitionedMap {
	  /**
	   * @param {Map} a
	   * @param {Map} b
	   * @param {Set} bKeys
	   */
	  constructor(a, b, bKeys) {
	    this.a = a;
	    this.b = b;
	    this.bKeys = bKeys;
	    this[Symbol.iterator] = this.entries;
	  }
	  get(key) {
	    return this.bKeys.has(key) ? this.b.get(key) : this.a.get(key);
	  }
	  set(key, value) {
	    if (this.bKeys.has(key)) {
	      this.b.set(key, value);
	    } else {
	      this.a.set(key, value);
	    }
	    return this;
	  }
	  has(key) {
	    return this.b.has(key) || this.a.has(key);
	  }
	  keys() {
	    return new Set([...this.a.keys(), ...this.b.keys()])[Symbol.iterator]();
	  }
	  entries() {
	    return mapIterator(this.keys(), key => [key, this.get(key)]);
	  }
	  forEach(callback) {
	    for (const key of this.keys()) {
	      callback(this.get(key), key, this);
	    }
	  }
	  delete(key) {
	    return this.bKeys.has(key) ? this.b.delete(key) : this.a.delete(key);
	  }
	  clear() {
	    this.a.clear();
	    this.b.clear();
	  }
	  get size() {
	    return [...this.keys()].length;
	  }
	}

	/**
	 * Create a new iterator that maps over the provided iterator, applying a mapping function to each item
	 */
	map$2.PartitionedMap = PartitionedMap;
	function mapIterator(it, callback) {
	  return {
	    next: () => {
	      const n = it.next();
	      return n.done ? n : {
	        value: callback(n.value),
	        done: false
	      };
	    }
	  };
	}

	/**
	 * Creates an empty map, or whatever your platform's polyfill is.
	 *
	 * @returns an empty Map or Map like object.
	 */
	function createEmptyMap() {
	  return new Map();
	}

	/**
	 * Creates a Map from the given object.
	 *
	 * @param { Map | { [key: string]: unknown } | undefined } mapOrObject
	 * @returns
	 */
	function createMap(mapOrObject) {
	  if (!mapOrObject) {
	    return createEmptyMap();
	  }
	  if ((0, _is.isMap)(mapOrObject)) {
	    return mapOrObject;
	  }
	  if ((0, _is.isObject)(mapOrObject)) {
	    return new ObjectWrappingMap(mapOrObject);
	  }
	  throw new Error('createMap can create maps from objects or Maps');
	}

	/**
	 * Unwraps a map into an object.
	 *
	 * @param {Map} map
	 * @returns { [key: string]: unknown }
	 */
	function toObject(map) {
	  if (map instanceof ObjectWrappingMap) {
	    return map.wrappedObject;
	  }
	  const object = {};
	  for (const key of map.keys()) {
	    const value = map.get(key);
	    (0, _customs.setSafeProperty)(object, key, value);
	  }
	  return object;
	}

	/**
	 * Copies the contents of key-value pairs from each `objects` in to `map`.
	 *
	 * Object is `objects` can be a `Map` or object.
	 *
	 * This is the `Map` analog to `Object.assign`.
	 */
	function assign(map) {
	  for (var _len = arguments.length, objects = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
	    objects[_key - 1] = arguments[_key];
	  }
	  for (const args of objects) {
	    if (!args) {
	      continue;
	    }
	    if ((0, _is.isMap)(args)) {
	      for (const key of args.keys()) {
	        map.set(key, args.get(key));
	      }
	    } else if ((0, _is.isObject)(args)) {
	      for (const key of Object.keys(args)) {
	        map.set(key, args[key]);
	      }
	    }
	  }
	  return map;
	}
	return map$2;
}

var hasRequiredIs;

function requireIs () {
	if (hasRequiredIs) return is;
	hasRequiredIs = 1;

	Object.defineProperty(is, "__esModule", {
	  value: true
	});
	is.isAccessorNode = isAccessorNode;
	is.isArray = void 0;
	is.isArrayNode = isArrayNode;
	is.isAssignmentNode = isAssignmentNode;
	is.isBigInt = isBigInt;
	is.isBigNumber = isBigNumber;
	is.isBlockNode = isBlockNode;
	is.isBoolean = isBoolean;
	is.isChain = isChain;
	is.isCollection = isCollection;
	is.isComplex = isComplex;
	is.isConditionalNode = isConditionalNode;
	is.isConstantNode = isConstantNode;
	is.isDate = isDate;
	is.isDenseMatrix = isDenseMatrix;
	is.isFraction = isFraction;
	is.isFunction = isFunction;
	is.isFunctionAssignmentNode = isFunctionAssignmentNode;
	is.isFunctionNode = isFunctionNode;
	is.isHelp = isHelp;
	is.isIndex = isIndex;
	is.isIndexNode = isIndexNode;
	is.isMap = isMap;
	is.isMatrix = isMatrix;
	is.isNode = isNode;
	is.isNull = isNull;
	is.isNumber = isNumber;
	is.isObject = isObject;
	is.isObjectNode = isObjectNode;
	is.isObjectWrappingMap = isObjectWrappingMap;
	is.isOperatorNode = isOperatorNode;
	is.isParenthesisNode = isParenthesisNode;
	is.isPartitionedMap = isPartitionedMap;
	is.isRange = isRange;
	is.isRangeNode = isRangeNode;
	is.isRegExp = isRegExp;
	is.isRelationalNode = isRelationalNode;
	is.isResultSet = isResultSet;
	is.isSparseMatrix = isSparseMatrix;
	is.isString = isString;
	is.isSymbolNode = isSymbolNode;
	is.isUndefined = isUndefined;
	is.isUnit = isUnit;
	is.rule2Node = rule2Node;
	is.typeOf = typeOf;
	var _map = requireMap$2();
	// type checks for all known types
	//
	// note that:
	//
	// - check by duck-typing on a property like `isUnit`, instead of checking instanceof.
	//   instanceof cannot be used because that would not allow to pass data from
	//   one instance of math.js to another since each has it's own instance of Unit.
	// - check the `isUnit` property via the constructor, so there will be no
	//   matches for "fake" instances like plain objects with a property `isUnit`.
	//   That is important for security reasons.
	// - It must not be possible to override the type checks used internally,
	//   for security reasons, so these functions are not exposed in the expression
	//   parser.

	function isNumber(x) {
	  return typeof x === 'number';
	}
	function isBigNumber(x) {
	  if (!x || typeof x !== 'object' || typeof x.constructor !== 'function') {
	    return false;
	  }
	  if (x.isBigNumber === true && typeof x.constructor.prototype === 'object' && x.constructor.prototype.isBigNumber === true) {
	    return true;
	  }
	  if (typeof x.constructor.isDecimal === 'function' && x.constructor.isDecimal(x) === true) {
	    return true;
	  }
	  return false;
	}
	function isBigInt(x) {
	  return typeof x === 'bigint';
	}
	function isComplex(x) {
	  return x && typeof x === 'object' && Object.getPrototypeOf(x).isComplex === true || false;
	}
	function isFraction(x) {
	  return x && typeof x === 'object' && Object.getPrototypeOf(x).isFraction === true || false;
	}
	function isUnit(x) {
	  return x && x.constructor.prototype.isUnit === true || false;
	}
	function isString(x) {
	  return typeof x === 'string';
	}
	is.isArray = Array.isArray;
	function isMatrix(x) {
	  return x && x.constructor.prototype.isMatrix === true || false;
	}

	/**
	 * Test whether a value is a collection: an Array or Matrix
	 * @param {*} x
	 * @returns {boolean} isCollection
	 */
	function isCollection(x) {
	  return Array.isArray(x) || isMatrix(x);
	}
	function isDenseMatrix(x) {
	  return x && x.isDenseMatrix && x.constructor.prototype.isMatrix === true || false;
	}
	function isSparseMatrix(x) {
	  return x && x.isSparseMatrix && x.constructor.prototype.isMatrix === true || false;
	}
	function isRange(x) {
	  return x && x.constructor.prototype.isRange === true || false;
	}
	function isIndex(x) {
	  return x && x.constructor.prototype.isIndex === true || false;
	}
	function isBoolean(x) {
	  return typeof x === 'boolean';
	}
	function isResultSet(x) {
	  return x && x.constructor.prototype.isResultSet === true || false;
	}
	function isHelp(x) {
	  return x && x.constructor.prototype.isHelp === true || false;
	}
	function isFunction(x) {
	  return typeof x === 'function';
	}
	function isDate(x) {
	  return x instanceof Date;
	}
	function isRegExp(x) {
	  return x instanceof RegExp;
	}
	function isObject(x) {
	  return !!(x && typeof x === 'object' && x.constructor === Object && !isComplex(x) && !isFraction(x));
	}

	/**
	 * Returns `true` if the passed object appears to be a Map (i.e. duck typing).
	 *
	 * Methods looked for are `get`, `set`, `keys` and `has`.
	 *
	 * @param {Map | object} object
	 * @returns
	 */
	function isMap(object) {
	  // We can use the fast instanceof, or a slower duck typing check.
	  // The duck typing method needs to cover enough methods to not be confused with DenseMatrix.
	  if (!object) {
	    return false;
	  }
	  return object instanceof Map || object instanceof _map.ObjectWrappingMap || typeof object.set === 'function' && typeof object.get === 'function' && typeof object.keys === 'function' && typeof object.has === 'function';
	}
	function isPartitionedMap(object) {
	  return isMap(object) && isMap(object.a) && isMap(object.b);
	}
	function isObjectWrappingMap(object) {
	  return isMap(object) && isObject(object.wrappedObject);
	}
	function isNull(x) {
	  return x === null;
	}
	function isUndefined(x) {
	  return x === undefined;
	}
	function isAccessorNode(x) {
	  return x && x.isAccessorNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isArrayNode(x) {
	  return x && x.isArrayNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isAssignmentNode(x) {
	  return x && x.isAssignmentNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isBlockNode(x) {
	  return x && x.isBlockNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isConditionalNode(x) {
	  return x && x.isConditionalNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isConstantNode(x) {
	  return x && x.isConstantNode === true && x.constructor.prototype.isNode === true || false;
	}

	/* Very specialized: returns true for those nodes which in the numerator of
	   a fraction means that the division in that fraction has precedence over implicit
	   multiplication, e.g. -2/3 x parses as (-2/3) x and 3/4 x parses as (3/4) x but
	   6!/8 x parses as 6! / (8x). It is located here because it is shared between
	   parse.js and OperatorNode.js (for parsing and printing, respectively).

	   This should *not* be exported from mathjs, unlike most of the tests here.
	   Its name does not start with 'is' to prevent utils/snapshot.js from thinking
	   it should be exported.
	*/
	function rule2Node(node) {
	  return isConstantNode(node) || isOperatorNode(node) && node.args.length === 1 && isConstantNode(node.args[0]) && '-+~'.includes(node.op);
	}
	function isFunctionAssignmentNode(x) {
	  return x && x.isFunctionAssignmentNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isFunctionNode(x) {
	  return x && x.isFunctionNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isIndexNode(x) {
	  return x && x.isIndexNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isNode(x) {
	  return x && x.isNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isObjectNode(x) {
	  return x && x.isObjectNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isOperatorNode(x) {
	  return x && x.isOperatorNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isParenthesisNode(x) {
	  return x && x.isParenthesisNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isRangeNode(x) {
	  return x && x.isRangeNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isRelationalNode(x) {
	  return x && x.isRelationalNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isSymbolNode(x) {
	  return x && x.isSymbolNode === true && x.constructor.prototype.isNode === true || false;
	}
	function isChain(x) {
	  return x && x.constructor.prototype.isChain === true || false;
	}
	function typeOf(x) {
	  const t = typeof x;
	  if (t === 'object') {
	    if (x === null) return 'null';
	    if (isBigNumber(x)) return 'BigNumber'; // Special: weird mashup with Decimal
	    if (x.constructor && x.constructor.name) return x.constructor.name;
	    return 'Object'; // just in case
	  }
	  return t; // can be 'string', 'number', 'boolean', 'function', 'bigint', ...
	}
	return is;
}

var hasRequiredObject$3;

function requireObject$3 () {
	if (hasRequiredObject$3) return object$3;
	hasRequiredObject$3 = 1;

	Object.defineProperty(object$3, "__esModule", {
	  value: true
	});
	object$3.canDefineProperty = canDefineProperty;
	object$3.clone = clone;
	object$3.deepExtend = deepExtend;
	object$3.deepFlatten = deepFlatten;
	object$3.deepStrictEqual = deepStrictEqual;
	object$3.extend = extend;
	object$3.get = get;
	object$3.hasOwnProperty = hasOwnProperty;
	object$3.isLegacyFactory = isLegacyFactory;
	object$3.lazy = lazy;
	object$3.mapObject = mapObject;
	object$3.pick = pick;
	object$3.pickShallow = pickShallow;
	object$3.set = set;
	object$3.traverse = traverse;
	var _is = requireIs();
	/**
	 * Clone an object
	 *
	 *     clone(x)
	 *
	 * Can clone any primitive type, array, and object.
	 * If x has a function clone, this function will be invoked to clone the object.
	 *
	 * @param {*} x
	 * @return {*} clone
	 */
	function clone(x) {
	  const type = typeof x;

	  // immutable primitive types
	  if (type === 'number' || type === 'bigint' || type === 'string' || type === 'boolean' || x === null || x === undefined) {
	    return x;
	  }

	  // use clone function of the object when available
	  if (typeof x.clone === 'function') {
	    return x.clone();
	  }

	  // array
	  if (Array.isArray(x)) {
	    return x.map(function (value) {
	      return clone(value);
	    });
	  }
	  if (x instanceof Date) return new Date(x.valueOf());
	  if ((0, _is.isBigNumber)(x)) return x; // bignumbers are immutable

	  // object
	  if ((0, _is.isObject)(x)) {
	    return mapObject(x, clone);
	  }
	  if (type === 'function') {
	    // we assume that the function is immutable
	    return x;
	  }
	  throw new TypeError(`Cannot clone: unknown type of value (value: ${x})`);
	}

	/**
	 * Apply map to all properties of an object
	 * @param {Object} object
	 * @param {function} callback
	 * @return {Object} Returns a copy of the object with mapped properties
	 */
	function mapObject(object, callback) {
	  const clone = {};
	  for (const key in object) {
	    if (hasOwnProperty(object, key)) {
	      clone[key] = callback(object[key]);
	    }
	  }
	  return clone;
	}

	/**
	 * Extend object a with the properties of object b
	 * @param {Object} a
	 * @param {Object} b
	 * @return {Object} a
	 */
	function extend(a, b) {
	  for (const prop in b) {
	    if (hasOwnProperty(b, prop)) {
	      a[prop] = b[prop];
	    }
	  }
	  return a;
	}

	/**
	 * Deep extend an object a with the properties of object b
	 * @param {Object} a
	 * @param {Object} b
	 * @returns {Object}
	 */
	function deepExtend(a, b) {
	  // TODO: add support for Arrays to deepExtend
	  if (Array.isArray(b)) {
	    throw new TypeError('Arrays are not supported by deepExtend');
	  }
	  for (const prop in b) {
	    // We check against prop not being in Object.prototype or Function.prototype
	    // to prevent polluting for example Object.__proto__.
	    if (hasOwnProperty(b, prop) && !(prop in Object.prototype) && !(prop in Function.prototype)) {
	      if (b[prop] && b[prop].constructor === Object) {
	        if (a[prop] === undefined) {
	          a[prop] = {};
	        }
	        if (a[prop] && a[prop].constructor === Object) {
	          deepExtend(a[prop], b[prop]);
	        } else {
	          a[prop] = b[prop];
	        }
	      } else if (Array.isArray(b[prop])) {
	        throw new TypeError('Arrays are not supported by deepExtend');
	      } else {
	        a[prop] = b[prop];
	      }
	    }
	  }
	  return a;
	}

	/**
	 * Deep test equality of all fields in two pairs of arrays or objects.
	 * Compares values and functions strictly (ie. 2 is not the same as '2').
	 * @param {Array | Object} a
	 * @param {Array | Object} b
	 * @returns {boolean}
	 */
	function deepStrictEqual(a, b) {
	  let prop, i, len;
	  if (Array.isArray(a)) {
	    if (!Array.isArray(b)) {
	      return false;
	    }
	    if (a.length !== b.length) {
	      return false;
	    }
	    for (i = 0, len = a.length; i < len; i++) {
	      if (!deepStrictEqual(a[i], b[i])) {
	        return false;
	      }
	    }
	    return true;
	  } else if (typeof a === 'function') {
	    return a === b;
	  } else if (a instanceof Object) {
	    if (Array.isArray(b) || !(b instanceof Object)) {
	      return false;
	    }
	    for (prop in a) {
	      // noinspection JSUnfilteredForInLoop
	      if (!(prop in b) || !deepStrictEqual(a[prop], b[prop])) {
	        return false;
	      }
	    }
	    for (prop in b) {
	      // noinspection JSUnfilteredForInLoop
	      if (!(prop in a)) {
	        return false;
	      }
	    }
	    return true;
	  } else {
	    return a === b;
	  }
	}

	/**
	 * Recursively flatten a nested object.
	 * @param {Object} nestedObject
	 * @return {Object} Returns the flattened object
	 */
	function deepFlatten(nestedObject) {
	  const flattenedObject = {};
	  _deepFlatten(nestedObject, flattenedObject);
	  return flattenedObject;
	}

	// helper function used by deepFlatten
	function _deepFlatten(nestedObject, flattenedObject) {
	  for (const prop in nestedObject) {
	    if (hasOwnProperty(nestedObject, prop)) {
	      const value = nestedObject[prop];
	      if (typeof value === 'object' && value !== null) {
	        _deepFlatten(value, flattenedObject);
	      } else {
	        flattenedObject[prop] = value;
	      }
	    }
	  }
	}

	/**
	 * Test whether the current JavaScript engine supports Object.defineProperty
	 * @returns {boolean} returns true if supported
	 */
	function canDefineProperty() {
	  // test needed for broken IE8 implementation
	  try {
	    if (Object.defineProperty) {
	      Object.defineProperty({}, 'x', {
	        get: function () {
	          return null;
	        }
	      });
	      return true;
	    }
	  } catch (e) {}
	  return false;
	}

	/**
	 * Attach a lazy loading property to a constant.
	 * The given function `fn` is called once when the property is first requested.
	 *
	 * @param {Object} object         Object where to add the property
	 * @param {string} prop           Property name
	 * @param {Function} valueResolver Function returning the property value. Called
	 *                                without arguments.
	 */
	function lazy(object, prop, valueResolver) {
	  let _uninitialized = true;
	  let _value;
	  Object.defineProperty(object, prop, {
	    get: function () {
	      if (_uninitialized) {
	        _value = valueResolver();
	        _uninitialized = false;
	      }
	      return _value;
	    },
	    set: function (value) {
	      _value = value;
	      _uninitialized = false;
	    },
	    configurable: true,
	    enumerable: true
	  });
	}

	/**
	 * Traverse a path into an object.
	 * When a namespace is missing, it will be created
	 * @param {Object} object
	 * @param {string | string[]} path   A dot separated string like 'name.space'
	 * @return {Object} Returns the object at the end of the path
	 */
	function traverse(object, path) {
	  if (path && typeof path === 'string') {
	    return traverse(object, path.split('.'));
	  }
	  let obj = object;
	  if (path) {
	    for (let i = 0; i < path.length; i++) {
	      const key = path[i];
	      if (!(key in obj)) {
	        obj[key] = {};
	      }
	      obj = obj[key];
	    }
	  }
	  return obj;
	}

	/**
	 * A safe hasOwnProperty
	 * @param {Object} object
	 * @param {string} property
	 */
	function hasOwnProperty(object, property) {
	  return object && Object.hasOwnProperty.call(object, property);
	}

	/**
	 * Test whether an object is a factory. a factory has fields:
	 *
	 * - factory: function (type: Object, config: Object, load: function, typed: function [, math: Object])   (required)
	 * - name: string (optional)
	 * - path: string    A dot separated path (optional)
	 * - math: boolean   If true (false by default), the math namespace is passed
	 *                   as fifth argument of the factory function
	 *
	 * @param {*} object
	 * @returns {boolean}
	 */
	function isLegacyFactory(object) {
	  return object && typeof object.factory === 'function';
	}

	/**
	 * Get a nested property from an object
	 * @param {Object} object
	 * @param {string | string[]} path
	 * @returns {Object}
	 */
	function get(object, path) {
	  if (typeof path === 'string') {
	    if (isPath(path)) {
	      return get(object, path.split('.'));
	    } else {
	      return object[path];
	    }
	  }
	  let child = object;
	  for (let i = 0; i < path.length; i++) {
	    const key = path[i];
	    child = child ? child[key] : undefined;
	  }
	  return child;
	}

	/**
	 * Set a nested property in an object
	 * Mutates the object itself
	 * If the path doesn't exist, it will be created
	 * @param {Object} object
	 * @param {string | string[]} path
	 * @param {*} value
	 * @returns {Object}
	 */
	function set(object, path, value) {
	  if (typeof path === 'string') {
	    if (isPath(path)) {
	      return set(object, path.split('.'), value);
	    } else {
	      object[path] = value;
	      return object;
	    }
	  }
	  let child = object;
	  for (let i = 0; i < path.length - 1; i++) {
	    const key = path[i];
	    if (child[key] === undefined) {
	      child[key] = {};
	    }
	    child = child[key];
	  }
	  if (path.length > 0) {
	    const lastKey = path[path.length - 1];
	    child[lastKey] = value;
	  }
	  return object;
	}

	/**
	 * Create an object composed of the picked object properties
	 * @param {Object} object
	 * @param {string[]} properties
	 * @param {function} [transform] Optional value to transform a value when picking it
	 * @return {Object}
	 */
	function pick(object, properties, transform) {
	  const copy = {};
	  for (let i = 0; i < properties.length; i++) {
	    const key = properties[i];
	    const value = get(object, key);
	    if (value !== undefined) {
	      set(copy, key, transform ? transform(value, key) : value);
	    }
	  }
	  return copy;
	}

	/**
	 * Shallow version of pick, creating an object composed of the picked object properties
	 * but not for nested properties
	 * @param {Object} object
	 * @param {string[]} properties
	 * @return {Object}
	 */
	function pickShallow(object, properties) {
	  const copy = {};
	  for (let i = 0; i < properties.length; i++) {
	    const key = properties[i];
	    const value = object[key];
	    if (value !== undefined) {
	      copy[key] = value;
	    }
	  }
	  return copy;
	}

	// helper function to test whether a string contains a path like 'user.name'
	function isPath(str) {
	  return str.includes('.');
	}
	return object$3;
}

var hasRequiredConfig$1;

function requireConfig$1 () {
	if (hasRequiredConfig$1) return config$2;
	hasRequiredConfig$1 = 1;

	Object.defineProperty(config$2, "__esModule", {
	  value: true
	});
	config$2.NUMBER_OPTIONS = config$2.MATRIX_OPTIONS = void 0;
	config$2.configFactory = configFactory;
	var _object = requireObject$3();
	var _config2 = requireConfig$2();
	const MATRIX_OPTIONS = config$2.MATRIX_OPTIONS = ['Matrix', 'Array']; // valid values for option matrix
	const NUMBER_OPTIONS = config$2.NUMBER_OPTIONS = ['number', 'BigNumber', 'bigint', 'Fraction']; // valid values for option number

	function configFactory(config, emit) {
	  /**
	   * Set configuration options for math.js, and get current options.
	   * Will emit a 'config' event, with arguments (curr, prev, changes).
	   *
	   * This function is only available on a mathjs instance created using `create`.
	   *
	   * Syntax:
	   *
	   *     math.config(config: Object): Object
	   *
	   * Examples:
	   *
	   *     import { create, all } from 'mathjs'
	   *
	   *     // create a mathjs instance
	   *     const math = create(all)
	   *
	   *     math.config().number                // outputs 'number'
	   *     math.evaluate('0.4')                // outputs number 0.4
	   *     math.config({number: 'Fraction'})
	   *     math.evaluate('0.4')                // outputs Fraction 2/5
	   *
	   * @param {Object} [options] Available options:
	   *                            {number} relTol
	   *                              Minimum relative difference between two
	   *                              compared values, used by all comparison functions.
	   *                            {number} absTol
	   *                              Minimum absolute difference between two
	   *                              compared values, used by all comparison functions.
	   *                            {string} matrix
	   *                              A string 'Matrix' (default) or 'Array'.
	   *                            {string} number
	   *                              A string 'number' (default), 'BigNumber', 'bigint', or 'Fraction'
	   *                            {number} precision
	   *                              The number of significant digits for BigNumbers.
	   *                              Not applicable for Numbers.
	   *                            {string} parenthesis
	   *                              How to display parentheses in LaTeX and string
	   *                              output.
	   *                            {string} randomSeed
	   *                              Random seed for seeded pseudo random number generator.
	   *                              Set to null to randomly seed.
	   * @return {Object} Returns the current configuration
	   */
	  function _config(options) {
	    if (options) {
	      if (options.epsilon !== undefined) {
	        // this if is only for backwards compatibility, it can be removed in the future.
	        console.warn('Warning: The configuration option "epsilon" is deprecated. Use "relTol" and "absTol" instead.');
	        const optionsFix = (0, _object.clone)(options);
	        optionsFix.relTol = options.epsilon;
	        optionsFix.absTol = options.epsilon * 1e-3;
	        delete optionsFix.epsilon;
	        return _config(optionsFix);
	      }
	      if (options.legacySubset === true) {
	        // this if is only for backwards compatibility, it can be removed in the future.
	        console.warn('Warning: The configuration option "legacySubset" is for compatibility only and might be deprecated in the future.');
	      }
	      const prev = (0, _object.clone)(config);

	      // validate some of the options
	      validateOption(options, 'matrix', MATRIX_OPTIONS);
	      validateOption(options, 'number', NUMBER_OPTIONS);

	      // merge options
	      (0, _object.deepExtend)(config, options);
	      const curr = (0, _object.clone)(config);
	      const changes = (0, _object.clone)(options);

	      // emit 'config' event
	      emit('config', curr, prev, changes);
	      return curr;
	    } else {
	      return (0, _object.clone)(config);
	    }
	  }

	  // attach the valid options to the function so they can be extended
	  _config.MATRIX_OPTIONS = MATRIX_OPTIONS;
	  _config.NUMBER_OPTIONS = NUMBER_OPTIONS;

	  // attach the config properties as readonly properties to the config function
	  Object.keys(_config2.DEFAULT_CONFIG).forEach(key => {
	    Object.defineProperty(_config, key, {
	      get: () => config[key],
	      enumerable: true,
	      configurable: true
	    });
	  });
	  return _config;
	}

	/**
	 * Validate an option
	 * @param {Object} options         Object with options
	 * @param {string} name            Name of the option to validate
	 * @param {Array.<string>} values  Array with valid values for this option
	 */
	function validateOption(options, name, values) {
	  if (options[name] !== undefined && !values.includes(options[name])) {
	    // unknown value
	    console.warn('Warning: Unknown value "' + options[name] + '" for configuration option "' + name + '". ' + 'Available options: ' + values.map(value => JSON.stringify(value)).join(', ') + '.');
	  }
	}
	return config$2;
}

var hasRequiredConfigReadonly;

function requireConfigReadonly () {
	if (hasRequiredConfigReadonly) return configReadonly;
	hasRequiredConfigReadonly = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(configReadonly, "__esModule", {
	  value: true
	});
	configReadonly.config = void 0;
	var _extends2 = _interopRequireDefault(require_extends());
	var _config = requireConfig$2();
	var _config2 = requireConfig$1();
	// create a read-only version of config
	const config = function (options) {
	  if (options) {
	    throw new Error('The global config is readonly. \n' + 'Please create a mathjs instance if you want to change the default configuration. \n' + 'Example:\n' + '\n' + '  import { create, all } from \'mathjs\';\n' + '  const mathjs = create(all);\n' + '  mathjs.config({ number: \'BigNumber\' });\n');
	  }
	  return Object.freeze(_config.DEFAULT_CONFIG);
	};
	configReadonly.config = config;
	(0, _extends2.default)(config, _config.DEFAULT_CONFIG, {
	  MATRIX_OPTIONS: _config2.MATRIX_OPTIONS,
	  NUMBER_OPTIONS: _config2.NUMBER_OPTIONS
	});
	return configReadonly;
}

var pureFunctionsAny_generated = {};

var factoriesAny = {};

var typed$1 = {};

function ok() {
  return true;
}
function notOk() {
  return false;
}
function undef() {
  return undefined;
}
const NOT_TYPED_FUNCTION = 'Argument is not a typed-function.';

/**
 * @typedef {{
 *   params: Param[],
 *   fn: function,
 *   test: function,
 *   implementation: function
 * }} Signature
 *
 * @typedef {{
 *   types: Type[],
 *   hasAny: boolean,
 *   hasConversion: boolean,
 *   restParam: boolean
 * }} Param
 *
 * @typedef {{
 *   name: string,
 *   typeIndex: number,
 *   test: function,
 *   isAny: boolean,
 *   conversion?: ConversionDef,
 *   conversionIndex: number,
 * }} Type
 *
 * @typedef {{
 *   from: string,
 *   to: string,
 *   convert: function (*) : *
 * }} ConversionDef
 *
 * @typedef {{
 *   name: string,
 *   test: function(*) : boolean,
 *   isAny?: boolean
 * }} TypeDef
 */

/**
 * @returns {() => function}
 */
function create$1() {
  // data type tests

  /**
   * Returns true if the argument is a non-null "plain" object
   */
  function isPlainObject(x) {
    return typeof x === 'object' && x !== null && x.constructor === Object;
  }
  const _types = [{
    name: 'number',
    test: function (x) {
      return typeof x === 'number';
    }
  }, {
    name: 'string',
    test: function (x) {
      return typeof x === 'string';
    }
  }, {
    name: 'boolean',
    test: function (x) {
      return typeof x === 'boolean';
    }
  }, {
    name: 'Function',
    test: function (x) {
      return typeof x === 'function';
    }
  }, {
    name: 'Array',
    test: Array.isArray
  }, {
    name: 'Date',
    test: function (x) {
      return x instanceof Date;
    }
  }, {
    name: 'RegExp',
    test: function (x) {
      return x instanceof RegExp;
    }
  }, {
    name: 'Object',
    test: isPlainObject
  }, {
    name: 'null',
    test: function (x) {
      return x === null;
    }
  }, {
    name: 'undefined',
    test: function (x) {
      return x === undefined;
    }
  }];
  const anyType = {
    name: 'any',
    test: ok,
    isAny: true
  };

  // Data structures to track the types. As these are local variables in
  // create(), each typed universe will get its own copy, but the variables
  // will only be accessible through the (closures of the) functions supplied
  // as properties of the typed object, not directly.
  // These will be initialized in clear() below
  let typeMap; // primary store of all types
  let typeList; // Array of just type names, for the sake of ordering

  // And similar data structures for the type conversions:
  let nConversions = 0;
  // the actual conversions are stored on a property of the destination types

  // This is a temporary object, will be replaced with a function at the end
  let typed = {
    createCount: 0
  };

  /**
   * Takes a type name and returns the corresponding official type object
   * for that type.
   *
   * @param {string} typeName
   * @returns {TypeDef} type
   */
  function findType(typeName) {
    const type = typeMap.get(typeName);
    if (type) {
      return type;
    }
    // Remainder is error handling
    let message = 'Unknown type "' + typeName + '"';
    const name = typeName.toLowerCase();
    let otherName;
    for (otherName of typeList) {
      if (otherName.toLowerCase() === name) {
        message += '. Did you mean "' + otherName + '" ?';
        break;
      }
    }
    throw new TypeError(message);
  }

  /**
   * Adds an array `types` of type definitions to this typed instance.
   * Each type definition should be an object with properties:
   * 'name' - a string giving the name of the type; 'test' - function
   * returning a boolean that tests membership in the type; and optionally
   * 'isAny' - true only for the 'any' type.
   *
   * The second optional argument, `before`, gives the name of a type that
   * these types should be added before. The new types are added in the
   * order specified.
   * @param {TypeDef[]} types
   * @param {string | boolean} [beforeSpec='any'] before
   */
  function addTypes(types) {
    let beforeSpec = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'any';
    const beforeIndex = beforeSpec ? findType(beforeSpec).index : typeList.length;
    const newTypes = [];
    for (let i = 0; i < types.length; ++i) {
      if (!types[i] || typeof types[i].name !== 'string' || typeof types[i].test !== 'function') {
        throw new TypeError('Object with properties {name: string, test: function} expected');
      }
      const typeName = types[i].name;
      if (typeMap.has(typeName)) {
        throw new TypeError('Duplicate type name "' + typeName + '"');
      }
      newTypes.push(typeName);
      typeMap.set(typeName, {
        name: typeName,
        test: types[i].test,
        isAny: types[i].isAny,
        index: beforeIndex + i,
        conversionsTo: [] // Newly added type can't have any conversions to it
      });
    }
    // update the typeList
    const affectedTypes = typeList.slice(beforeIndex);
    typeList = typeList.slice(0, beforeIndex).concat(newTypes).concat(affectedTypes);
    // Fix the indices
    for (let i = beforeIndex + newTypes.length; i < typeList.length; ++i) {
      typeMap.get(typeList[i]).index = i;
    }
  }

  /**
   * Removes all types and conversions from this typed instance.
   * May cause previously constructed typed-functions to throw
   * strange errors when they are called with types that do not
   * match any of their signatures.
   */
  function clear() {
    typeMap = new Map();
    typeList = [];
    nConversions = 0;
    addTypes([anyType], false);
  }

  // initialize the types to the default list
  clear();
  addTypes(_types);

  /**
   * Removes all conversions, leaving the types alone.
   */
  function clearConversions() {
    let typeName;
    for (typeName of typeList) {
      typeMap.get(typeName).conversionsTo = [];
    }
    nConversions = 0;
  }

  /**
   * Find the type names that match a value.
   * @param {*} value
   * @return {string[]} Array of names of types for which
   *                  the type test matches the value.
   */
  function findTypeNames(value) {
    const matches = typeList.filter(name => {
      const type = typeMap.get(name);
      return !type.isAny && type.test(value);
    });
    if (matches.length) {
      return matches;
    }
    return ['any'];
  }

  /**
   * Check if an entity is a typed function created by any instance
   * @param {any} entity
   * @returns {boolean}
   */
  function isTypedFunction(entity) {
    return entity && typeof entity === 'function' && '_typedFunctionData' in entity;
  }

  /**
   * Find a specific signature from a (composed) typed function, for example:
   *
   *   typed.findSignature(fn, ['number', 'string'])
   *   typed.findSignature(fn, 'number, string')
   *   typed.findSignature(fn, 'number,string', {exact: true})
   *
   * This function findSignature will by default return the best match to
   * the given signature, possibly employing type conversions.
   *
   * The (optional) third argument is a plain object giving options
   * controlling the signature search. Currently the only implemented
   * option is `exact`: if specified as true (default is false), only
   * exact matches will be returned (i.e. signatures for which `fn` was
   * directly defined). Note that a (possibly different) type matching
   * `any`, or one or more instances of TYPE matching `...TYPE` are
   * considered exact matches in this regard, as no conversions are used.
   *
   * This function returns a "signature" object, as does `typed.resolve()`,
   * which is a plain object with four keys: `params` (the array of parameters
   * for this signature), `fn` (the originally supplied function for this
   * signature), `test` (a generated function that determines if an argument
   * list matches this signature, and `implementation` (the function to call
   * on a matching argument list, that performs conversions if necessary and
   * then calls the originally supplied function).
   *
   * @param {Function} fn                   A typed-function
   * @param {string | string[]} signature
   *     Signature to be found, can be an array or a comma separated string.
   * @param {object} options  Controls the signature search as documented
   * @return {{ params: Param[], fn: function, test: function, implementation: function }}
   *     Returns the matching signature, or throws an error when no signature
   *     is found.
   */
  function findSignature(fn, signature, options) {
    if (!isTypedFunction(fn)) {
      throw new TypeError(NOT_TYPED_FUNCTION);
    }

    // Canonicalize input
    const exact = options && options.exact;
    const stringSignature = Array.isArray(signature) ? signature.join(',') : signature;
    const params = parseSignature(stringSignature);
    const canonicalSignature = stringifyParams(params);

    // First hope we get lucky and exactly match a signature
    if (!exact || canonicalSignature in fn.signatures) {
      // OK, we can check the internal signatures
      const match = fn._typedFunctionData.signatureMap.get(canonicalSignature);
      if (match) {
        return match;
      }
    }

    // Oh well, we did not; so we have to go back and check the parameters
    // one by one, in order to catch things like `any` and rest params.
    // Note here we can assume there is at least one parameter, because
    // the empty signature would have matched successfully above.
    const nParams = params.length;
    let remainingSignatures;
    if (exact) {
      remainingSignatures = [];
      let name;
      for (name in fn.signatures) {
        remainingSignatures.push(fn._typedFunctionData.signatureMap.get(name));
      }
    } else {
      remainingSignatures = fn._typedFunctionData.signatures;
    }
    for (let i = 0; i < nParams; ++i) {
      const want = params[i];
      const filteredSignatures = [];
      let possibility;
      for (possibility of remainingSignatures) {
        const have = getParamAtIndex(possibility.params, i);
        if (!have || want.restParam && !have.restParam) {
          continue;
        }
        if (!have.hasAny) {
          // have to check all of the wanted types are available
          const haveTypes = paramTypeSet(have);
          if (want.types.some(wtype => !haveTypes.has(wtype.name))) {
            continue;
          }
        }
        // OK, this looks good
        filteredSignatures.push(possibility);
      }
      remainingSignatures = filteredSignatures;
      if (remainingSignatures.length === 0) break;
    }
    // Return the first remaining signature that was totally matched:
    let candidate;
    for (candidate of remainingSignatures) {
      if (candidate.params.length <= nParams) {
        return candidate;
      }
    }
    throw new TypeError('Signature not found (signature: ' + (fn.name || 'unnamed') + '(' + stringifyParams(params, ', ') + '))');
  }

  /**
   * Find the proper function to call for a specific signature from
   * a (composed) typed function, for example:
   *
   *   typed.find(fn, ['number', 'string'])
   *   typed.find(fn, 'number, string')
   *   typed.find(fn, 'number,string', {exact: true})
   *
   * This function find will by default return the best match to
   * the given signature, possibly employing type conversions (and returning
   * a function that will perform those conversions as needed). The
   * (optional) third argument is a plain object giving options contolling
   * the signature search. Currently only the option `exact` is implemented,
   * which defaults to "false". If `exact` is specified as true, then only
   * exact matches will be returned (i.e. signatures for which `fn` was
   * directly defined). Uses of `any` and `...TYPE` are considered exact if
   * no conversions are necessary to apply the corresponding function.
   *
   * @param {Function} fn                   A typed-function
   * @param {string | string[]} signature
   *     Signature to be found, can be an array or a comma separated string.
   * @param {object} options  Controls the signature match as documented
   * @return {function}
   *     Returns the function to call for the given signature, or throws an
   *     error if no match is found.
   */
  function find(fn, signature, options) {
    return findSignature(fn, signature, options).implementation;
  }

  /**
   * Convert a given value to another data type, specified by type name.
   *
   * @param {*} value
   * @param {string} typeName
   */
  function convert(value, typeName) {
    // check conversion is needed
    const type = findType(typeName);
    if (type.test(value)) {
      return value;
    }
    const conversions = type.conversionsTo;
    if (conversions.length === 0) {
      throw new Error('There are no conversions to ' + typeName + ' defined.');
    }
    for (let i = 0; i < conversions.length; i++) {
      const fromType = findType(conversions[i].from);
      if (fromType.test(value)) {
        return conversions[i].convert(value);
      }
    }
    throw new Error('Cannot convert ' + value + ' to ' + typeName);
  }

  /**
   * Stringify parameters in a normalized way
   * @param {Param[]} params
   * @param {string} [','] separator
   * @return {string}
   */
  function stringifyParams(params) {
    let separator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ',';
    return params.map(p => p.name).join(separator);
  }

  /**
   * Parse a parameter, like "...number | boolean"
   * @param {string} param
   * @return {Param} param
   */
  function parseParam(param) {
    const restParam = param.indexOf('...') === 0;
    const types = !restParam ? param : param.length > 3 ? param.slice(3) : 'any';
    const typeDefs = types.split('|').map(s => findType(s.trim()));
    let hasAny = false;
    let paramName = restParam ? '...' : '';
    const exactTypes = typeDefs.map(function (type) {
      hasAny = type.isAny || hasAny;
      paramName += type.name + '|';
      return {
        name: type.name,
        typeIndex: type.index,
        test: type.test,
        isAny: type.isAny,
        conversion: null,
        conversionIndex: -1
      };
    });
    return {
      types: exactTypes,
      name: paramName.slice(0, -1),
      // remove trailing '|' from above
      hasAny,
      hasConversion: false,
      restParam
    };
  }

  /**
   * Expands a parsed parameter with the types available from currently
   * defined conversions.
   * @param {Param} param
   * @return {Param} param
   */
  function expandParam(param) {
    const typeNames = param.types.map(t => t.name);
    const matchingConversions = availableConversions(typeNames);
    let hasAny = param.hasAny;
    let newName = param.name;
    const convertibleTypes = matchingConversions.map(function (conversion) {
      const type = findType(conversion.from);
      hasAny = type.isAny || hasAny;
      newName += '|' + conversion.from;
      return {
        name: conversion.from,
        typeIndex: type.index,
        test: type.test,
        isAny: type.isAny,
        conversion,
        conversionIndex: conversion.index
      };
    });
    return {
      types: param.types.concat(convertibleTypes),
      name: newName,
      hasAny,
      hasConversion: convertibleTypes.length > 0,
      restParam: param.restParam
    };
  }

  /**
   * Return the set of type names in a parameter.
   * Caches the result for efficiency
   *
   * @param {Param} param
   * @return {Set<string>} typenames
   */
  function paramTypeSet(param) {
    if (!param.typeSet) {
      param.typeSet = new Set();
      param.types.forEach(type => param.typeSet.add(type.name));
    }
    return param.typeSet;
  }

  /**
   * Parse a signature with comma separated parameters,
   * like "number | boolean, ...string"
   *
   * @param {string} signature
   * @return {Param[]} params
   */
  function parseSignature(rawSignature) {
    const params = [];
    if (typeof rawSignature !== 'string') {
      throw new TypeError('Signatures must be strings');
    }
    const signature = rawSignature.trim();
    if (signature === '') {
      return params;
    }
    const rawParams = signature.split(',');
    for (let i = 0; i < rawParams.length; ++i) {
      const parsedParam = parseParam(rawParams[i].trim());
      if (parsedParam.restParam && i !== rawParams.length - 1) {
        throw new SyntaxError('Unexpected rest parameter "' + rawParams[i] + '": ' + 'only allowed for the last parameter');
      }
      // if invalid, short-circuit (all the types may have been filtered)
      if (parsedParam.types.length === 0) {
        return null;
      }
      params.push(parsedParam);
    }
    return params;
  }

  /**
   * Test whether a set of params contains a restParam
   * @param {Param[]} params
   * @return {boolean} Returns true when the last parameter is a restParam
   */
  function hasRestParam(params) {
    const param = last(params);
    return param ? param.restParam : false;
  }

  /**
   * Create a type test for a single parameter, which can have one or multiple
   * types.
   * @param {Param} param
   * @return {function(x: *) : boolean} Returns a test function
   */
  function compileTest(param) {
    if (!param || param.types.length === 0) {
      // nothing to do
      return ok;
    } else if (param.types.length === 1) {
      return findType(param.types[0].name).test;
    } else if (param.types.length === 2) {
      const test0 = findType(param.types[0].name).test;
      const test1 = findType(param.types[1].name).test;
      return function or(x) {
        return test0(x) || test1(x);
      };
    } else {
      // param.types.length > 2
      const tests = param.types.map(function (type) {
        return findType(type.name).test;
      });
      return function or(x) {
        for (let i = 0; i < tests.length; i++) {
          if (tests[i](x)) {
            return true;
          }
        }
        return false;
      };
    }
  }

  /**
   * Create a test for all parameters of a signature
   * @param {Param[]} params
   * @return {function(args: Array<*>) : boolean}
   */
  function compileTests(params) {
    let tests, test0, test1;
    if (hasRestParam(params)) {
      // variable arguments like '...number'
      tests = initial(params).map(compileTest);
      const varIndex = tests.length;
      const lastTest = compileTest(last(params));
      const testRestParam = function (args) {
        for (let i = varIndex; i < args.length; i++) {
          if (!lastTest(args[i])) {
            return false;
          }
        }
        return true;
      };
      return function testArgs(args) {
        for (let i = 0; i < tests.length; i++) {
          if (!tests[i](args[i])) {
            return false;
          }
        }
        return testRestParam(args) && args.length >= varIndex + 1;
      };
    } else {
      // no variable arguments
      if (params.length === 0) {
        return function testArgs(args) {
          return args.length === 0;
        };
      } else if (params.length === 1) {
        test0 = compileTest(params[0]);
        return function testArgs(args) {
          return test0(args[0]) && args.length === 1;
        };
      } else if (params.length === 2) {
        test0 = compileTest(params[0]);
        test1 = compileTest(params[1]);
        return function testArgs(args) {
          return test0(args[0]) && test1(args[1]) && args.length === 2;
        };
      } else {
        // arguments.length > 2
        tests = params.map(compileTest);
        return function testArgs(args) {
          for (let i = 0; i < tests.length; i++) {
            if (!tests[i](args[i])) {
              return false;
            }
          }
          return args.length === tests.length;
        };
      }
    }
  }

  /**
   * Find the parameter at a specific index of a Params list.
   * Handles rest parameters.
   * @param {Param[]} params
   * @param {number} index
   * @return {Param | null} Returns the matching parameter when found,
   *                        null otherwise.
   */
  function getParamAtIndex(params, index) {
    return index < params.length ? params[index] : hasRestParam(params) ? last(params) : null;
  }

  /**
   * Get all type names of a parameter
   * @param {Params[]} params
   * @param {number} index
   * @return {string[]} Returns an array with type names
   */
  function getTypeSetAtIndex(params, index) {
    const param = getParamAtIndex(params, index);
    if (!param) {
      return new Set();
    }
    return paramTypeSet(param);
  }

  /**
   * Test whether a type is an exact type or conversion
   * @param {Type} type
   * @return {boolean} Returns true when
   */
  function isExactType(type) {
    return type.conversion === null || type.conversion === undefined;
  }

  /**
   * Helper function for creating error messages: create an array with
   * all available types on a specific argument index.
   * @param {Signature[]} signatures
   * @param {number} index
   * @return {string[]} Returns an array with available types
   */
  function mergeExpectedParams(signatures, index) {
    const typeSet = new Set();
    signatures.forEach(signature => {
      const paramSet = getTypeSetAtIndex(signature.params, index);
      let name;
      for (name of paramSet) {
        typeSet.add(name);
      }
    });
    return typeSet.has('any') ? ['any'] : Array.from(typeSet);
  }

  /**
   * Create
   * @param {string} name             The name of the function
   * @param {array.<*>} args          The actual arguments passed to the function
   * @param {Signature[]} signatures  A list with available signatures
   * @return {TypeError} Returns a type error with additional data
   *                     attached to it in the property `data`
   */
  function createError(name, args, signatures) {
    let err, expected;
    const _name = name || 'unnamed';

    // test for wrong type at some index
    let matchingSignatures = signatures;
    let index;
    for (index = 0; index < args.length; index++) {
      const nextMatchingDefs = [];
      matchingSignatures.forEach(signature => {
        const param = getParamAtIndex(signature.params, index);
        const test = compileTest(param);
        if ((index < signature.params.length || hasRestParam(signature.params)) && test(args[index])) {
          nextMatchingDefs.push(signature);
        }
      });
      if (nextMatchingDefs.length === 0) {
        // no matching signatures anymore, throw error "wrong type"
        expected = mergeExpectedParams(matchingSignatures, index);
        if (expected.length > 0) {
          const actualTypes = findTypeNames(args[index]);
          err = new TypeError('Unexpected type of argument in function ' + _name + ' (expected: ' + expected.join(' or ') + ', actual: ' + actualTypes.join(' | ') + ', index: ' + index + ')');
          err.data = {
            category: 'wrongType',
            fn: _name,
            index,
            actual: actualTypes,
            expected
          };
          return err;
        }
      } else {
        matchingSignatures = nextMatchingDefs;
      }
    }

    // test for too few arguments
    const lengths = matchingSignatures.map(function (signature) {
      return hasRestParam(signature.params) ? Infinity : signature.params.length;
    });
    if (args.length < Math.min.apply(null, lengths)) {
      expected = mergeExpectedParams(matchingSignatures, index);
      err = new TypeError('Too few arguments in function ' + _name + ' (expected: ' + expected.join(' or ') + ', index: ' + args.length + ')');
      err.data = {
        category: 'tooFewArgs',
        fn: _name,
        index: args.length,
        expected
      };
      return err;
    }

    // test for too many arguments
    const maxLength = Math.max.apply(null, lengths);
    if (args.length > maxLength) {
      err = new TypeError('Too many arguments in function ' + _name + ' (expected: ' + maxLength + ', actual: ' + args.length + ')');
      err.data = {
        category: 'tooManyArgs',
        fn: _name,
        index: args.length,
        expectedLength: maxLength
      };
      return err;
    }

    // Generic error
    const argTypes = [];
    for (let i = 0; i < args.length; ++i) {
      argTypes.push(findTypeNames(args[i]).join('|'));
    }
    err = new TypeError('Arguments of type "' + argTypes.join(', ') + '" do not match any of the defined signatures of function ' + _name + '.');
    err.data = {
      category: 'mismatch',
      actual: argTypes
    };
    return err;
  }

  /**
   * Find the lowest index of all exact types of a parameter (no conversions)
   * @param {Param} param
   * @return {number} Returns the index of the lowest type in typed.types
   */
  function getLowestTypeIndex(param) {
    let min = typeList.length + 1;
    for (let i = 0; i < param.types.length; i++) {
      if (isExactType(param.types[i])) {
        min = Math.min(min, param.types[i].typeIndex);
      }
    }
    return min;
  }

  /**
   * Find the lowest index of the conversion of all types of the parameter
   * having a conversion
   * @param {Param} param
   * @return {number} Returns the lowest index of the conversions of this type
   */
  function getLowestConversionIndex(param) {
    let min = nConversions + 1;
    for (let i = 0; i < param.types.length; i++) {
      if (!isExactType(param.types[i])) {
        min = Math.min(min, param.types[i].conversionIndex);
      }
    }
    return min;
  }

  /**
   * Compare two params
   * @param {Param} param1
   * @param {Param} param2
   * @return {number} returns -1 when param1 must get a lower
   *                  index than param2, 1 when the opposite,
   *                  or zero when both are equal
   */
  function compareParams(param1, param2) {
    // We compare a number of metrics on a param in turn:
    // 1) 'any' parameters are the least preferred
    if (param1.hasAny) {
      if (!param2.hasAny) {
        return 1;
      }
    } else if (param2.hasAny) {
      return -1;
    }

    // 2) Prefer non-rest to rest parameters
    if (param1.restParam) {
      if (!param2.restParam) {
        return 1;
      }
    } else if (param2.restParam) {
      return -1;
    }

    // 3) Prefer exact type match to conversions
    if (param1.hasConversion) {
      if (!param2.hasConversion) {
        return 1;
      }
    } else if (param2.hasConversion) {
      return -1;
    }

    // 4) Prefer lower type index:
    const typeDiff = getLowestTypeIndex(param1) - getLowestTypeIndex(param2);
    if (typeDiff < 0) {
      return -1;
    }
    if (typeDiff > 0) {
      return 1;
    }

    // 5) Prefer lower conversion index
    const convDiff = getLowestConversionIndex(param1) - getLowestConversionIndex(param2);
    if (convDiff < 0) {
      return -1;
    }
    if (convDiff > 0) {
      return 1;
    }

    // Don't have a basis for preference
    return 0;
  }

  /**
   * Compare two signatures
   * @param {Signature} signature1
   * @param {Signature} signature2
   * @return {number} returns a negative number when param1 must get a lower
   *                  index than param2, a positive number when the opposite,
   *                  or zero when both are equal
   */
  function compareSignatures(signature1, signature2) {
    const pars1 = signature1.params;
    const pars2 = signature2.params;
    const last1 = last(pars1);
    const last2 = last(pars2);
    const hasRest1 = hasRestParam(pars1);
    const hasRest2 = hasRestParam(pars2);
    // We compare a number of metrics on signatures in turn:
    // 1) An "any rest param" is least preferred
    if (hasRest1 && last1.hasAny) {
      if (!hasRest2 || !last2.hasAny) {
        return 1;
      }
    } else if (hasRest2 && last2.hasAny) {
      return -1;
    }

    // 2) Minimize the number of 'any' parameters
    let any1 = 0;
    let conv1 = 0;
    let par;
    for (par of pars1) {
      if (par.hasAny) ++any1;
      if (par.hasConversion) ++conv1;
    }
    let any2 = 0;
    let conv2 = 0;
    for (par of pars2) {
      if (par.hasAny) ++any2;
      if (par.hasConversion) ++conv2;
    }
    if (any1 !== any2) {
      return any1 - any2;
    }

    // 3) A conversion rest param is less preferred
    if (hasRest1 && last1.hasConversion) {
      if (!hasRest2 || !last2.hasConversion) {
        return 1;
      }
    } else if (hasRest2 && last2.hasConversion) {
      return -1;
    }

    // 4) Minimize the number of conversions
    if (conv1 !== conv2) {
      return conv1 - conv2;
    }

    // 5) Prefer no rest param
    if (hasRest1) {
      if (!hasRest2) {
        return 1;
      }
    } else if (hasRest2) {
      return -1;
    }

    // 6) Prefer shorter with rest param, longer without
    const lengthCriterion = (pars1.length - pars2.length) * (hasRest1 ? -1 : 1);
    if (lengthCriterion !== 0) {
      return lengthCriterion;
    }

    // Signatures are identical in each of the above metrics.
    // In particular, they are the same length.
    // We can therefore compare the parameters one by one.
    // First we count which signature has more preferred parameters.
    const comparisons = [];
    let tc = 0;
    for (let i = 0; i < pars1.length; ++i) {
      const thisComparison = compareParams(pars1[i], pars2[i]);
      comparisons.push(thisComparison);
      tc += thisComparison;
    }
    if (tc !== 0) {
      return tc;
    }

    // They have the same number of preferred parameters, so go by the
    // earliest parameter in which we have a preference.
    // In other words, dispatch is driven somewhat more by earlier
    // parameters than later ones.
    let c;
    for (c of comparisons) {
      if (c !== 0) {
        return c;
      }
    }

    // It's a tossup:
    return 0;
  }

  /**
   * Produce a list of all conversions from distinct types to one of
   * the given types.
   *
   * @param {string[]} typeNames
   * @return {ConversionDef[]} Returns the conversions that are available
   *                        resulting in any given type (if any)
   */
  function availableConversions(typeNames) {
    if (typeNames.length === 0) {
      return [];
    }
    const types = typeNames.map(findType);
    if (typeNames.length > 1) {
      types.sort((t1, t2) => t1.index - t2.index);
    }
    let matches = types[0].conversionsTo;
    if (typeNames.length === 1) {
      return matches;
    }
    matches = matches.concat([]); // shallow copy the matches
    // Since the types are now in index order, we just want the first
    // occurrence of any from type:
    const knownTypes = new Set(typeNames);
    for (let i = 1; i < types.length; ++i) {
      let newMatch;
      for (newMatch of types[i].conversionsTo) {
        if (!knownTypes.has(newMatch.from)) {
          matches.push(newMatch);
          knownTypes.add(newMatch.from);
        }
      }
    }
    return matches;
  }

  /**
   * Preprocess arguments before calling the original function:
   * - if needed convert the parameters
   * - in case of rest parameters, move the rest parameters into an Array
   * @param {Param[]} params
   * @param {function} fn
   * @return {function} Returns a wrapped function
   */
  function compileArgsPreprocessing(params, fn) {
    let fnConvert = fn;

    // TODO: can we make this wrapper function smarter/simpler?

    if (params.some(p => p.hasConversion)) {
      const restParam = hasRestParam(params);
      const compiledConversions = params.map(compileArgConversion);
      fnConvert = function convertArgs() {
        const args = [];
        const last = restParam ? arguments.length - 1 : arguments.length;
        for (let i = 0; i < last; i++) {
          args[i] = compiledConversions[i](arguments[i]);
        }
        if (restParam) {
          args[last] = arguments[last].map(compiledConversions[last]);
        }
        return fn.apply(this, args);
      };
    }
    let fnPreprocess = fnConvert;
    if (hasRestParam(params)) {
      const offset = params.length - 1;
      fnPreprocess = function preprocessRestParams() {
        return fnConvert.apply(this, slice(arguments, 0, offset).concat([slice(arguments, offset)]));
      };
    }
    return fnPreprocess;
  }

  /**
   * Compile conversion for a parameter to the right type
   * @param {Param} param
   * @return {function} Returns the wrapped function that will convert arguments
   *
   */
  function compileArgConversion(param) {
    let test0, test1, conversion0, conversion1;
    const tests = [];
    const conversions = [];
    param.types.forEach(function (type) {
      if (type.conversion) {
        tests.push(findType(type.conversion.from).test);
        conversions.push(type.conversion.convert);
      }
    });

    // create optimized conversion functions depending on the number of conversions
    switch (conversions.length) {
      case 0:
        return function convertArg(arg) {
          return arg;
        };
      case 1:
        test0 = tests[0];
        conversion0 = conversions[0];
        return function convertArg(arg) {
          if (test0(arg)) {
            return conversion0(arg);
          }
          return arg;
        };
      case 2:
        test0 = tests[0];
        test1 = tests[1];
        conversion0 = conversions[0];
        conversion1 = conversions[1];
        return function convertArg(arg) {
          if (test0(arg)) {
            return conversion0(arg);
          }
          if (test1(arg)) {
            return conversion1(arg);
          }
          return arg;
        };
      default:
        return function convertArg(arg) {
          for (let i = 0; i < conversions.length; i++) {
            if (tests[i](arg)) {
              return conversions[i](arg);
            }
          }
          return arg;
        };
    }
  }

  /**
   * Split params with union types in to separate params.
   *
   * For example:
   *
   *     splitParams([['Array', 'Object'], ['string', 'RegExp'])
   *     // returns:
   *     // [
   *     //   ['Array', 'string'],
   *     //   ['Array', 'RegExp'],
   *     //   ['Object', 'string'],
   *     //   ['Object', 'RegExp']
   *     // ]
   *
   * @param {Param[]} params
   * @return {Param[]}
   */
  function splitParams(params) {
    function _splitParams(params, index, paramsSoFar) {
      if (index < params.length) {
        const param = params[index];
        let resultingParams = [];
        if (param.restParam) {
          // split the types of a rest parameter in two:
          // one with only exact types, and one with exact types and conversions
          const exactTypes = param.types.filter(isExactType);
          if (exactTypes.length < param.types.length) {
            resultingParams.push({
              types: exactTypes,
              name: '...' + exactTypes.map(t => t.name).join('|'),
              hasAny: exactTypes.some(t => t.isAny),
              hasConversion: false,
              restParam: true
            });
          }
          resultingParams.push(param);
        } else {
          // split all the types of a regular parameter into one type per param
          resultingParams = param.types.map(function (type) {
            return {
              types: [type],
              name: type.name,
              hasAny: type.isAny,
              hasConversion: type.conversion,
              restParam: false
            };
          });
        }

        // recurse over the groups with types
        return flatMap(resultingParams, function (nextParam) {
          return _splitParams(params, index + 1, paramsSoFar.concat([nextParam]));
        });
      } else {
        // we've reached the end of the parameters.
        return [paramsSoFar];
      }
    }
    return _splitParams(params, 0, []);
  }

  /**
   * Test whether two param lists represent conflicting signatures
   * @param {Param[]} params1
   * @param {Param[]} params2
   * @return {boolean} Returns true when the signatures conflict, false otherwise.
   */
  function conflicting(params1, params2) {
    const ii = Math.max(params1.length, params2.length);
    for (let i = 0; i < ii; i++) {
      const typeSet1 = getTypeSetAtIndex(params1, i);
      const typeSet2 = getTypeSetAtIndex(params2, i);
      let overlap = false;
      let name;
      for (name of typeSet2) {
        if (typeSet1.has(name)) {
          overlap = true;
          break;
        }
      }
      if (!overlap) {
        return false;
      }
    }
    const len1 = params1.length;
    const len2 = params2.length;
    const restParam1 = hasRestParam(params1);
    const restParam2 = hasRestParam(params2);
    return restParam1 ? restParam2 ? len1 === len2 : len2 >= len1 : restParam2 ? len1 >= len2 : len1 === len2;
  }

  /**
   * Helper function for `resolveReferences` that returns a copy of
   * functionList wihe any prior resolutions cleared out, in case we are
   * recycling signatures from a prior typed function construction.
   *
   * @param {Array.<function|typed-reference>} functionList
   * @return {Array.<function|typed-reference>}
   */
  function clearResolutions(functionList) {
    return functionList.map(fn => {
      if (isReferToSelf(fn)) {
        return referToSelf(fn.referToSelf.callback);
      }
      if (isReferTo(fn)) {
        return makeReferTo(fn.referTo.references, fn.referTo.callback);
      }
      return fn;
    });
  }

  /**
   * Take a list of references, a list of functions functionList, and a
   * signatureMap indexing signatures into functionList, and return
   * the list of resolutions, or a false-y value if they don't all
   * resolve in a valid way (yet).
   *
   * @param {string[]} references
   * @param {Array<function|typed-reference} functionList
   * @param {Object.<string, integer>} signatureMap
   * @return {function[] | false} resolutions
   */
  function collectResolutions(references, functionList, signatureMap) {
    const resolvedReferences = [];
    let reference;
    for (reference of references) {
      let resolution = signatureMap[reference];
      if (typeof resolution !== 'number') {
        throw new TypeError('No definition for referenced signature "' + reference + '"');
      }
      resolution = functionList[resolution];
      if (typeof resolution !== 'function') {
        return false;
      }
      resolvedReferences.push(resolution);
    }
    return resolvedReferences;
  }

  /**
   * Resolve any references in the functionList for the typed function
   * itself. The signatureMap tells which index in the functionList a
   * given signature should be mapped to (for use in resolving typed.referTo)
   * and self provides the destions of a typed.referToSelf.
   *
   * @param {Array<function | typed-reference-object>} functionList
   * @param {Object.<string, function>} signatureMap
   * @param {function} self  The typed-function itself
   * @return {Array<function>} The list of resolved functions
   */
  function resolveReferences(functionList, signatureMap, self) {
    const resolvedFunctions = clearResolutions(functionList);
    const isResolved = new Array(resolvedFunctions.length).fill(false);
    let leftUnresolved = true;
    while (leftUnresolved) {
      leftUnresolved = false;
      let nothingResolved = true;
      for (let i = 0; i < resolvedFunctions.length; ++i) {
        if (isResolved[i]) continue;
        const fn = resolvedFunctions[i];
        if (isReferToSelf(fn)) {
          resolvedFunctions[i] = fn.referToSelf.callback(self);
          // Preserve reference in case signature is reused someday:
          resolvedFunctions[i].referToSelf = fn.referToSelf;
          isResolved[i] = true;
          nothingResolved = false;
        } else if (isReferTo(fn)) {
          const resolvedReferences = collectResolutions(fn.referTo.references, resolvedFunctions, signatureMap);
          if (resolvedReferences) {
            resolvedFunctions[i] = fn.referTo.callback.apply(this, resolvedReferences);
            // Preserve reference in case signature is reused someday:
            resolvedFunctions[i].referTo = fn.referTo;
            isResolved[i] = true;
            nothingResolved = false;
          } else {
            leftUnresolved = true;
          }
        }
      }
      if (nothingResolved && leftUnresolved) {
        throw new SyntaxError('Circular reference detected in resolving typed.referTo');
      }
    }
    return resolvedFunctions;
  }

  /**
   * Validate whether any of the function bodies contains a self-reference
   * usage like `this(...)` or `this.signatures`. This self-referencing is
   * deprecated since typed-function v3. It has been replaced with
   * the functions typed.referTo and typed.referToSelf.
   * @param {Object.<string, function>} signaturesMap
   */
  function validateDeprecatedThis(signaturesMap) {
    // TODO: remove this deprecation warning logic some day (it's introduced in v3)

    // match occurrences like 'this(' and 'this.signatures'
    const deprecatedThisRegex = /\bthis(\(|\.signatures\b)/;
    Object.keys(signaturesMap).forEach(signature => {
      const fn = signaturesMap[signature];
      if (deprecatedThisRegex.test(fn.toString())) {
        throw new SyntaxError('Using `this` to self-reference a function ' + 'is deprecated since typed-function@3. ' + 'Use typed.referTo and typed.referToSelf instead.');
      }
    });
  }

  /**
   * Create a typed function
   * @param {String} name               The name for the typed function
   * @param {Object.<string, function>} rawSignaturesMap
   *                                    An object with one or
   *                                    multiple signatures as key, and the
   *                                    function corresponding to the
   *                                    signature as value.
   * @return {function}  Returns the created typed function.
   */
  function createTypedFunction(name, rawSignaturesMap) {
    typed.createCount++;
    if (Object.keys(rawSignaturesMap).length === 0) {
      throw new SyntaxError('No signatures provided');
    }
    if (typed.warnAgainstDeprecatedThis) {
      validateDeprecatedThis(rawSignaturesMap);
    }

    // Main processing loop for signatures
    const parsedParams = [];
    const originalFunctions = [];
    const signaturesMap = {};
    const preliminarySignatures = []; // may have duplicates from conversions
    let signature;
    for (signature in rawSignaturesMap) {
      // A) Protect against polluted Object prototype:
      if (!Object.prototype.hasOwnProperty.call(rawSignaturesMap, signature)) {
        continue;
      }
      // B) Parse the signature
      const params = parseSignature(signature);
      if (!params) continue;
      // C) Check for conflicts
      parsedParams.forEach(function (pp) {
        if (conflicting(pp, params)) {
          throw new TypeError('Conflicting signatures "' + stringifyParams(pp) + '" and "' + stringifyParams(params) + '".');
        }
      });
      parsedParams.push(params);
      // D) Store the provided function and add conversions
      const functionIndex = originalFunctions.length;
      originalFunctions.push(rawSignaturesMap[signature]);
      const conversionParams = params.map(expandParam);
      // E) Split the signatures and collect them up
      let sp;
      for (sp of splitParams(conversionParams)) {
        const spName = stringifyParams(sp);
        preliminarySignatures.push({
          params: sp,
          name: spName,
          fn: functionIndex
        });
        if (sp.every(p => !p.hasConversion)) {
          signaturesMap[spName] = functionIndex;
        }
      }
    }
    preliminarySignatures.sort(compareSignatures);

    // Note the forward reference to theTypedFn
    const resolvedFunctions = resolveReferences(originalFunctions, signaturesMap, theTypedFn);

    // Fill in the proper function for each signature
    let s;
    for (s in signaturesMap) {
      if (Object.prototype.hasOwnProperty.call(signaturesMap, s)) {
        signaturesMap[s] = resolvedFunctions[signaturesMap[s]];
      }
    }
    const signatures = [];
    const internalSignatureMap = new Map(); // benchmarks faster than object
    for (s of preliminarySignatures) {
      // Note it's only safe to eliminate duplicates like this
      // _after_ the signature sorting step above; otherwise we might
      // remove the wrong one.
      if (!internalSignatureMap.has(s.name)) {
        s.fn = resolvedFunctions[s.fn];
        signatures.push(s);
        internalSignatureMap.set(s.name, s);
      }
    }

    // we create a highly optimized checks for the first couple of signatures with max 2 arguments
    const ok0 = signatures[0] && signatures[0].params.length <= 2 && !hasRestParam(signatures[0].params);
    const ok1 = signatures[1] && signatures[1].params.length <= 2 && !hasRestParam(signatures[1].params);
    const ok2 = signatures[2] && signatures[2].params.length <= 2 && !hasRestParam(signatures[2].params);
    const ok3 = signatures[3] && signatures[3].params.length <= 2 && !hasRestParam(signatures[3].params);
    const ok4 = signatures[4] && signatures[4].params.length <= 2 && !hasRestParam(signatures[4].params);
    const ok5 = signatures[5] && signatures[5].params.length <= 2 && !hasRestParam(signatures[5].params);
    const allOk = ok0 && ok1 && ok2 && ok3 && ok4 && ok5;

    // compile the tests
    for (let i = 0; i < signatures.length; ++i) {
      signatures[i].test = compileTests(signatures[i].params);
    }
    const test00 = ok0 ? compileTest(signatures[0].params[0]) : notOk;
    const test10 = ok1 ? compileTest(signatures[1].params[0]) : notOk;
    const test20 = ok2 ? compileTest(signatures[2].params[0]) : notOk;
    const test30 = ok3 ? compileTest(signatures[3].params[0]) : notOk;
    const test40 = ok4 ? compileTest(signatures[4].params[0]) : notOk;
    const test50 = ok5 ? compileTest(signatures[5].params[0]) : notOk;
    const test01 = ok0 ? compileTest(signatures[0].params[1]) : notOk;
    const test11 = ok1 ? compileTest(signatures[1].params[1]) : notOk;
    const test21 = ok2 ? compileTest(signatures[2].params[1]) : notOk;
    const test31 = ok3 ? compileTest(signatures[3].params[1]) : notOk;
    const test41 = ok4 ? compileTest(signatures[4].params[1]) : notOk;
    const test51 = ok5 ? compileTest(signatures[5].params[1]) : notOk;

    // compile the functions
    for (let i = 0; i < signatures.length; ++i) {
      signatures[i].implementation = compileArgsPreprocessing(signatures[i].params, signatures[i].fn);
    }
    const fn0 = ok0 ? signatures[0].implementation : undef;
    const fn1 = ok1 ? signatures[1].implementation : undef;
    const fn2 = ok2 ? signatures[2].implementation : undef;
    const fn3 = ok3 ? signatures[3].implementation : undef;
    const fn4 = ok4 ? signatures[4].implementation : undef;
    const fn5 = ok5 ? signatures[5].implementation : undef;
    const len0 = ok0 ? signatures[0].params.length : -1;
    const len1 = ok1 ? signatures[1].params.length : -1;
    const len2 = ok2 ? signatures[2].params.length : -1;
    const len3 = ok3 ? signatures[3].params.length : -1;
    const len4 = ok4 ? signatures[4].params.length : -1;
    const len5 = ok5 ? signatures[5].params.length : -1;

    // simple and generic, but also slow
    const iStart = allOk ? 6 : 0;
    const iEnd = signatures.length;
    // de-reference ahead for execution speed:
    const tests = signatures.map(s => s.test);
    const fns = signatures.map(s => s.implementation);
    const generic = function generic() {

      for (let i = iStart; i < iEnd; i++) {
        if (tests[i](arguments)) {
          return fns[i].apply(this, arguments);
        }
      }
      return typed.onMismatch(name, arguments, signatures);
    };

    // create the typed function
    // fast, specialized version. Falls back to the slower, generic one if needed
    function theTypedFn(arg0, arg1) {

      if (arguments.length === len0 && test00(arg0) && test01(arg1)) {
        return fn0.apply(this, arguments);
      }
      if (arguments.length === len1 && test10(arg0) && test11(arg1)) {
        return fn1.apply(this, arguments);
      }
      if (arguments.length === len2 && test20(arg0) && test21(arg1)) {
        return fn2.apply(this, arguments);
      }
      if (arguments.length === len3 && test30(arg0) && test31(arg1)) {
        return fn3.apply(this, arguments);
      }
      if (arguments.length === len4 && test40(arg0) && test41(arg1)) {
        return fn4.apply(this, arguments);
      }
      if (arguments.length === len5 && test50(arg0) && test51(arg1)) {
        return fn5.apply(this, arguments);
      }
      return generic.apply(this, arguments);
    }

    // attach name the typed function
    try {
      Object.defineProperty(theTypedFn, 'name', {
        value: name
      });
    } catch (err) {
      // old browsers do not support Object.defineProperty and some don't support setting the name property
      // the function name is not essential for the functioning, it's mostly useful for debugging,
      // so it's fine to have unnamed functions.
    }

    // attach signatures to the function.
    // This property is close to the original collection of signatures
    // used to create the typed-function, just with unions split:
    theTypedFn.signatures = signaturesMap;

    // Store internal data for functions like resolve, find, etc.
    // Also serves as the flag that this is a typed-function
    theTypedFn._typedFunctionData = {
      signatures,
      signatureMap: internalSignatureMap
    };
    return theTypedFn;
  }

  /**
   * Action to take on mismatch
   * @param {string} name      Name of function that was attempted to be called
   * @param {Array} args       Actual arguments to the call
   * @param {Array} signatures Known signatures of the named typed-function
   */
  function _onMismatch(name, args, signatures) {
    throw createError(name, args, signatures);
  }

  /**
   * Return all but the last items of an array or function Arguments
   * @param {Array | Arguments} arr
   * @return {Array}
   */
  function initial(arr) {
    return slice(arr, 0, arr.length - 1);
  }

  /**
   * return the last item of an array or function Arguments
   * @param {Array | Arguments} arr
   * @return {*}
   */
  function last(arr) {
    return arr[arr.length - 1];
  }

  /**
   * Slice an array or function Arguments
   * @param {Array | Arguments | IArguments} arr
   * @param {number} start
   * @param {number} [end]
   * @return {Array}
   */
  function slice(arr, start, end) {
    return Array.prototype.slice.call(arr, start, end);
  }

  /**
   * Return the first item from an array for which test(arr[i]) returns true
   * @param {Array} arr
   * @param {function} test
   * @return {* | undefined} Returns the first matching item
   *                         or undefined when there is no match
   */
  function findInArray(arr, test) {
    for (let i = 0; i < arr.length; i++) {
      if (test(arr[i])) {
        return arr[i];
      }
    }
    return undefined;
  }

  /**
   * Flat map the result invoking a callback for every item in an array.
   * https://gist.github.com/samgiles/762ee337dff48623e729
   * @param {Array} arr
   * @param {function} callback
   * @return {Array}
   */
  function flatMap(arr, callback) {
    return Array.prototype.concat.apply([], arr.map(callback));
  }

  /**
   * Create a reference callback to one or multiple signatures
   *
   * Syntax:
   *
   *     typed.referTo(signature1, signature2, ..., function callback(fn1, fn2, ...) {
   *       // ...
   *     })
   *
   * @returns {{referTo: {references: string[], callback}}}
   */
  function referTo() {
    const references = initial(arguments).map(s => stringifyParams(parseSignature(s)));
    const callback = last(arguments);
    if (typeof callback !== 'function') {
      throw new TypeError('Callback function expected as last argument');
    }
    return makeReferTo(references, callback);
  }
  function makeReferTo(references, callback) {
    return {
      referTo: {
        references,
        callback
      }
    };
  }

  /**
   * Create a reference callback to the typed-function itself
   *
   * @param {(self: function) => function} callback
   * @returns {{referToSelf: { callback: function }}}
   */
  function referToSelf(callback) {
    if (typeof callback !== 'function') {
      throw new TypeError('Callback function expected as first argument');
    }
    return {
      referToSelf: {
        callback
      }
    };
  }

  /**
   * Test whether something is a referTo object, holding a list with reference
   * signatures and a callback.
   *
   * @param {Object | function} objectOrFn
   * @returns {boolean}
   */
  function isReferTo(objectOrFn) {
    return objectOrFn && typeof objectOrFn.referTo === 'object' && Array.isArray(objectOrFn.referTo.references) && typeof objectOrFn.referTo.callback === 'function';
  }

  /**
   * Test whether something is a referToSelf object, holding a callback where
   * to pass `self`.
   *
   * @param {Object | function} objectOrFn
   * @returns {boolean}
   */
  function isReferToSelf(objectOrFn) {
    return objectOrFn && typeof objectOrFn.referToSelf === 'object' && typeof objectOrFn.referToSelf.callback === 'function';
  }

  /**
   * Check if name is (A) new, (B) a match, or (C) a mismatch; and throw
   * an error in case (C).
   *
   * @param { string | undefined } nameSoFar
   * @param { string | undefined } newName
   * @returns { string } updated name
   */
  function checkName(nameSoFar, newName) {
    if (!nameSoFar) {
      return newName;
    }
    if (newName && newName !== nameSoFar) {
      const err = new Error('Function names do not match (expected: ' + nameSoFar + ', actual: ' + newName + ')');
      err.data = {
        actual: newName,
        expected: nameSoFar
      };
      throw err;
    }
    return nameSoFar;
  }

  /**
   * Retrieve the implied name from an object with signature keys
   * and function values, checking whether all value names match
   *
   * @param { {string: function} } obj
   */
  function getObjectName(obj) {
    let name;
    for (const key in obj) {
      // Only pay attention to own properties, and only if their values
      // are typed functions or functions with a signature property
      if (Object.prototype.hasOwnProperty.call(obj, key) && (isTypedFunction(obj[key]) || typeof obj[key].signature === 'string')) {
        name = checkName(name, obj[key].name);
      }
    }
    return name;
  }

  /**
   * Copy all of the signatures from the second argument into the first,
   * which is modified by side effect, checking for conflicts
   *
   * @param {Object.<string, function|typed-reference>} dest
   * @param {Object.<string, function|typed-reference>} source
   */
  function mergeSignatures(dest, source) {
    let key;
    for (key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        if (key in dest) {
          if (source[key] !== dest[key]) {
            const err = new Error('Signature "' + key + '" is defined twice');
            err.data = {
              signature: key,
              sourceFunction: source[key],
              destFunction: dest[key]
            };
            throw err;
          }
          // else: both signatures point to the same function, that's fine
        }
        dest[key] = source[key];
      }
    }
  }
  const saveTyped = typed;

  /**
   * Originally the main function was a typed function itself, but then
   * it might not be able to generate error messages if the client
   * replaced the type system with different names.
   *
   * Main entry: typed([name], functions/objects with signatures...)
   *
   * Assembles and returns a new typed-function from the given items
   * that provide signatures and implementations, each of which may be
   * * a plain object mapping (string) signatures to implementing functions,
   * * a previously constructed typed function, or
   * * any other single function with a string-valued property `signature`.
    * The name of the resulting typed-function will be given by the
   * string-valued name argument if present, or if not, by the name
   * of any of the arguments that have one, as long as any that do are
   * consistent with each other. If no name is specified, the name will be
   * an empty string.
   *
   * @param {string} maybeName [optional]
   * @param {(function|object)[]} signature providers
   * @returns {typed-function}
   */
  typed = function (maybeName) {
    const named = typeof maybeName === 'string';
    const start = named ? 1 : 0;
    let name = named ? maybeName : '';
    const allSignatures = {};
    for (let i = start; i < arguments.length; ++i) {
      const item = arguments[i];
      let theseSignatures = {};
      let thisName;
      if (typeof item === 'function') {
        thisName = item.name;
        if (typeof item.signature === 'string') {
          // Case 1: Ordinary function with a string 'signature' property
          theseSignatures[item.signature] = item;
        } else if (isTypedFunction(item)) {
          // Case 2: Existing typed function
          theseSignatures = item.signatures;
        }
      } else if (isPlainObject(item)) {
        // Case 3: Plain object, assume keys = signatures, values = functions
        theseSignatures = item;
        if (!named) {
          thisName = getObjectName(item);
        }
      }
      if (Object.keys(theseSignatures).length === 0) {
        const err = new TypeError('Argument to \'typed\' at index ' + i + ' is not a (typed) function, ' + 'nor an object with signatures as keys and functions as values.');
        err.data = {
          index: i,
          argument: item
        };
        throw err;
      }
      if (!named) {
        name = checkName(name, thisName);
      }
      mergeSignatures(allSignatures, theseSignatures);
    }
    return createTypedFunction(name || '', allSignatures);
  };
  typed.create = create$1;
  typed.createCount = saveTyped.createCount;
  typed.onMismatch = _onMismatch;
  typed.throwMismatchError = _onMismatch;
  typed.createError = createError;
  typed.clear = clear;
  typed.clearConversions = clearConversions;
  typed.addTypes = addTypes;
  typed._findType = findType; // For unit testing only
  typed.referTo = referTo;
  typed.referToSelf = referToSelf;
  typed.convert = convert;
  typed.findSignature = findSignature;
  typed.find = find;
  typed.isTypedFunction = isTypedFunction;
  typed.warnAgainstDeprecatedThis = true;

  /**
   * add a type (convenience wrapper for typed.addTypes)
   * @param {{name: string, test: function}} type
   * @param {boolean} [beforeObjectTest=true]
   *                          If true, the new test will be inserted before
   *                          the test with name 'Object' (if any), since
   *                          tests for Object match Array and classes too.
   */
  typed.addType = function (type, beforeObjectTest) {
    let before = 'any';
    if (beforeObjectTest !== false && typeMap.has('Object')) {
      before = 'Object';
    }
    typed.addTypes([type], before);
  };

  /**
   * Verify that the ConversionDef conversion has a valid format.
   *
   * @param {conversionDef} conversion
   * @return {void}
   * @throws {TypeError|SyntaxError}
   */
  function _validateConversion(conversion) {
    if (!conversion || typeof conversion.from !== 'string' || typeof conversion.to !== 'string' || typeof conversion.convert !== 'function') {
      throw new TypeError('Object with properties {from: string, to: string, convert: function} expected');
    }
    if (conversion.to === conversion.from) {
      throw new SyntaxError('Illegal to define conversion from "' + conversion.from + '" to itself.');
    }
  }

  /**
   * Add a conversion
   *
   * @param {ConversionDef} conversion
   * @param {{override: boolean}} [options]
   * @returns {void}
   * @throws {TypeError}
   */
  typed.addConversion = function (conversion) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      override: false
    };
    _validateConversion(conversion);
    const to = findType(conversion.to);
    const existing = to.conversionsTo.find(other => other.from === conversion.from);
    if (existing) {
      if (options && options.override) {
        typed.removeConversion({
          from: existing.from,
          to: conversion.to,
          convert: existing.convert
        });
      } else {
        throw new Error('There is already a conversion from "' + conversion.from + '" to "' + to.name + '"');
      }
    }
    to.conversionsTo.push({
      from: conversion.from,
      convert: conversion.convert,
      index: nConversions++
    });
  };

  /**
   * Convenience wrapper to call addConversion on each conversion in a list.
   *
   * @param {ConversionDef[]} conversions
   * @param {{override: boolean}} [options]
   * @returns {void}
   * @throws {TypeError}
   */
  typed.addConversions = function (conversions, options) {
    conversions.forEach(conversion => typed.addConversion(conversion, options));
  };

  /**
   * Remove the specified conversion. The format is the same as for
   * addConversion, and the convert function must match or an error
   * is thrown.
   *
   * @param {{from: string, to: string, convert: function}} conversion
   * @returns {void}
   * @throws {TypeError|SyntaxError|Error}
   */
  typed.removeConversion = function (conversion) {
    _validateConversion(conversion);
    const to = findType(conversion.to);
    const existingConversion = findInArray(to.conversionsTo, c => c.from === conversion.from);
    if (!existingConversion) {
      throw new Error('Attempt to remove nonexistent conversion from ' + conversion.from + ' to ' + conversion.to);
    }
    if (existingConversion.convert !== conversion.convert) {
      throw new Error('Conversion to remove does not match existing conversion');
    }
    const index = to.conversionsTo.indexOf(existingConversion);
    to.conversionsTo.splice(index, 1);
  };

  /**
   * Produce the specific signature that a typed function
   * will execute on the given arguments. Here, a "signature" is an
   * object with properties 'params', 'test', 'fn', and 'implementation'.
   * This last property is a function that converts params as necessary
   * and then calls 'fn'. Returns null if there is no matching signature.
   * @param {typed-function} tf
   * @param {any[]} argList
   * @returns {{params: string, test: function, fn: function, implementation: function}}
   */
  typed.resolve = function (tf, argList) {
    if (!isTypedFunction(tf)) {
      throw new TypeError(NOT_TYPED_FUNCTION);
    }
    const sigs = tf._typedFunctionData.signatures;
    for (let i = 0; i < sigs.length; ++i) {
      if (sigs[i].test(argList)) {
        return sigs[i];
      }
    }
    return null;
  };
  return typed;
}
var typedFunction = create$1();

var typedFunction$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': typedFunction
});

var require$$2$1 = /*@__PURE__*/getAugmentedNamespace(typedFunction$1);

var factory = {};

var hasRequiredFactory$b;

function requireFactory$b () {
	if (hasRequiredFactory$b) return factory;
	hasRequiredFactory$b = 1;

	Object.defineProperty(factory, "__esModule", {
	  value: true
	});
	factory.assertDependencies = assertDependencies;
	factory.create = create;
	factory.factory = factory$1;
	factory.isFactory = isFactory;
	factory.isOptionalDependency = isOptionalDependency;
	factory.sortFactories = sortFactories;
	factory.stripOptionalNotation = stripOptionalNotation;
	var _object = requireObject$3();
	/**
	 * Create a factory function, which can be used to inject dependencies.
	 *
	 * The created functions are memoized, a consecutive call of the factory
	 * with the exact same inputs will return the same function instance.
	 * The memoized cache is exposed on `factory.cache` and can be cleared
	 * if needed.
	 *
	 * Example:
	 *
	 *     const name = 'log'
	 *     const dependencies = ['config', 'typed', 'divideScalar', 'Complex']
	 *
	 *     export const createLog = factory(name, dependencies, ({ typed, config, divideScalar, Complex }) => {
	 *       // ... create the function log here and return it
	 *     }
	 *
	 * @param {string} name           Name of the function to be created
	 * @param {string[]} dependencies The names of all required dependencies
	 * @param {function} create       Callback function called with an object with all dependencies
	 * @param {Object} [meta]
	 *     Optional object with meta information that will be attached
	 *     to the created factory function as property `meta`. For explanation
	 *     of what meta properties can be specified and what they mean, see
	 *     docs/core/extension.md.
	 * @returns {function}
	 */
	function factory$1(name, dependencies, create, meta) {
	  function assertAndCreate(scope) {
	    // we only pass the requested dependencies to the factory function
	    // to prevent functions to rely on dependencies that are not explicitly
	    // requested.
	    const deps = (0, _object.pickShallow)(scope, dependencies.map(stripOptionalNotation));
	    assertDependencies(name, dependencies, scope);
	    return create(deps);
	  }
	  assertAndCreate.isFactory = true;
	  assertAndCreate.fn = name;
	  assertAndCreate.dependencies = dependencies.slice().sort();
	  if (meta) {
	    assertAndCreate.meta = meta;
	  }
	  return assertAndCreate;
	}

	/**
	 * Sort all factories such that when loading in order, the dependencies are resolved.
	 *
	 * @param {Array} factories
	 * @returns {Array} Returns a new array with the sorted factories.
	 */
	function sortFactories(factories) {
	  const factoriesByName = {};
	  factories.forEach(factory => {
	    factoriesByName[factory.fn] = factory;
	  });
	  function containsDependency(factory, dependency) {
	    // TODO: detect circular references
	    if (isFactory(factory)) {
	      if (factory.dependencies.includes(dependency.fn || dependency.name)) {
	        return true;
	      }
	      if (factory.dependencies.some(d => containsDependency(factoriesByName[d], dependency))) {
	        return true;
	      }
	    }
	    return false;
	  }
	  const sorted = [];
	  function addFactory(factory) {
	    let index = 0;
	    while (index < sorted.length && !containsDependency(sorted[index], factory)) {
	      index++;
	    }
	    sorted.splice(index, 0, factory);
	  }

	  // sort regular factory functions
	  factories.filter(isFactory).forEach(addFactory);

	  // sort legacy factory functions AFTER the regular factory functions
	  factories.filter(factory => !isFactory(factory)).forEach(addFactory);
	  return sorted;
	}

	// TODO: comment or cleanup if unused in the end
	function create(factories) {
	  let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	  sortFactories(factories).forEach(factory => factory(scope));
	  return scope;
	}

	/**
	 * Test whether an object is a factory. This is the case when it has
	 * properties name, dependencies, and a function create.
	 * @param {*} obj
	 * @returns {boolean}
	 */
	function isFactory(obj) {
	  return typeof obj === 'function' && typeof obj.fn === 'string' && Array.isArray(obj.dependencies);
	}

	/**
	 * Assert that all dependencies of a list with dependencies are available in the provided scope.
	 *
	 * Will throw an exception when there are dependencies missing.
	 *
	 * @param {string} name   Name for the function to be created. Used to generate a useful error message
	 * @param {string[]} dependencies
	 * @param {Object} scope
	 */
	function assertDependencies(name, dependencies, scope) {
	  const allDefined = dependencies.filter(dependency => !isOptionalDependency(dependency)) // filter optionals
	  .every(dependency => scope[dependency] !== undefined);
	  if (!allDefined) {
	    const missingDependencies = dependencies.filter(dependency => scope[dependency] === undefined);

	    // TODO: create a custom error class for this, a MathjsError or something like that
	    throw new Error(`Cannot create function "${name}", ` + `some dependencies are missing: ${missingDependencies.map(d => `"${d}"`).join(', ')}.`);
	  }
	}
	function isOptionalDependency(dependency) {
	  return dependency && dependency[0] === '?';
	}
	function stripOptionalNotation(dependency) {
	  return dependency && dependency[0] === '?' ? dependency.slice(1) : dependency;
	}
	return factory;
}

var number$3 = {};

var hasRequiredNumber$3;

function requireNumber$3 () {
	if (hasRequiredNumber$3) return number$3;
	hasRequiredNumber$3 = 1;

	Object.defineProperty(number$3, "__esModule", {
	  value: true
	});
	number$3.cbrt = number$3.atanh = number$3.asinh = number$3.acosh = void 0;
	number$3.copysign = copysign;
	number$3.cosh = void 0;
	number$3.digits = digits;
	number$3.expm1 = void 0;
	number$3.format = format;
	number$3.isInteger = isInteger;
	number$3.log2 = number$3.log1p = number$3.log10 = void 0;
	number$3.nearlyEqual = nearlyEqual;
	number$3.normalizeFormatOptions = normalizeFormatOptions;
	number$3.roundDigits = roundDigits;
	number$3.safeNumberType = safeNumberType;
	number$3.sinh = number$3.sign = void 0;
	number$3.splitNumber = splitNumber;
	number$3.tanh = void 0;
	number$3.toEngineering = toEngineering;
	number$3.toExponential = toExponential;
	number$3.toFixed = toFixed;
	number$3.toPrecision = toPrecision;
	var _is = requireIs();
	/**
	 * @typedef {{sign: '+' | '-' | '', coefficients: number[], exponent: number}} SplitValue
	 */

	/**
	 * Check if a number is integer
	 * @param {number | boolean} value
	 * @return {boolean} isInteger
	 */
	function isInteger(value) {
	  if (typeof value === 'boolean') {
	    return true;
	  }
	  return Number.isFinite(value) ? value === Math.round(value) : false;
	}

	/**
	 * Ensure the number type is compatible with the provided value.
	 * If not, return 'number' instead.
	 *
	 * For example:
	 *
	 *     safeNumberType('2.3', { number: 'bigint', numberFallback: 'number' })
	 *
	 * will return 'number' and not 'bigint' because trying to create a bigint with
	 * value 2.3 would throw an exception.
	 *
	 * @param {string} numberStr
	 * @param {{
	 *   number: 'number' | 'BigNumber' | 'bigint' | 'Fraction'
	 *   numberFallback: 'number' | 'BigNumber'
	 * }} config
	 * @returns {'number' | 'BigNumber' | 'bigint' | 'Fraction'}
	 */
	function safeNumberType(numberStr, config) {
	  if (config.number === 'bigint') {
	    try {
	      BigInt(numberStr);
	    } catch (_unused) {
	      return config.numberFallback;
	    }
	  }
	  return config.number;
	}

	/**
	 * Calculate the sign of a number
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.sign = Math.sign || function (x) {
	  if (x > 0) {
	    return 1;
	  } else if (x < 0) {
	    return -1;
	  } else {
	    return 0;
	  }
	};

	/**
	 * Calculate the base-2 logarithm of a number
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.log2 = Math.log2 || function log2(x) {
	  return Math.log(x) / Math.LN2;
	};

	/**
	 * Calculate the base-10 logarithm of a number
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.log10 = Math.log10 || function log10(x) {
	  return Math.log(x) / Math.LN10;
	};

	/**
	 * Calculate the natural logarithm of a number + 1
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.log1p = Math.log1p || function (x) {
	  return Math.log(x + 1);
	};

	/**
	 * Calculate cubic root for a number
	 *
	 * Code from es6-shim.js:
	 *   https://github.com/paulmillr/es6-shim/blob/master/es6-shim.js#L1564-L1577
	 *
	 * @param {number} x
	 * @returns {number} Returns the cubic root of x
	 */
	number$3.cbrt = Math.cbrt || function cbrt(x) {
	  if (x === 0) {
	    return x;
	  }
	  const negate = x < 0;
	  let result;
	  if (negate) {
	    x = -x;
	  }
	  if (Number.isFinite(x)) {
	    result = Math.exp(Math.log(x) / 3);
	    // from https://en.wikipedia.org/wiki/Cube_root#Numerical_methods
	    result = (x / (result * result) + 2 * result) / 3;
	  } else {
	    result = x;
	  }
	  return negate ? -result : result;
	};

	/**
	 * Calculates exponentiation minus 1
	 * @param {number} x
	 * @return {number} res
	 */
	number$3.expm1 = Math.expm1 || function expm1(x) {
	  return x >= 2e-4 || x <= -2e-4 ? Math.exp(x) - 1 : x + x * x / 2 + x * x * x / 6;
	};

	/**
	 * Formats a number in a given base
	 * @param {number} n
	 * @param {number} base
	 * @param {number} size
	 * @returns {string}
	 */
	function formatNumberToBase(n, base, size) {
	  const prefixes = {
	    2: '0b',
	    8: '0o',
	    16: '0x'
	  };
	  const prefix = prefixes[base];
	  let suffix = '';
	  if (size) {
	    if (size < 1) {
	      throw new Error('size must be in greater than 0');
	    }
	    if (!isInteger(size)) {
	      throw new Error('size must be an integer');
	    }
	    if (n > 2 ** (size - 1) - 1 || n < -(2 ** (size - 1))) {
	      throw new Error(`Value must be in range [-2^${size - 1}, 2^${size - 1}-1]`);
	    }
	    if (!isInteger(n)) {
	      throw new Error('Value must be an integer');
	    }
	    if (n < 0) {
	      n = n + 2 ** size;
	    }
	    suffix = `i${size}`;
	  }
	  let sign = '';
	  if (n < 0) {
	    n = -n;
	    sign = '-';
	  }
	  return `${sign}${prefix}${n.toString(base)}${suffix}`;
	}

	/**
	 * Convert a number to a formatted string representation.
	 *
	 * Syntax:
	 *
	 *    format(value)
	 *    format(value, options)
	 *    format(value, precision)
	 *    format(value, fn)
	 *
	 * Where:
	 *
	 *    {number} value   The value to be formatted
	 *    {Object} options An object with formatting options. Available options:
	 *                     {string} notation
	 *                         Number notation. Choose from:
	 *                         'fixed'          Always use regular number notation.
	 *                                          For example '123.40' and '14000000'
	 *                         'exponential'    Always use exponential notation.
	 *                                          For example '1.234e+2' and '1.4e+7'
	 *                         'engineering'    Always use engineering notation.
	 *                                          For example '123.4e+0' and '14.0e+6'
	 *                         'auto' (default) Regular number notation for numbers
	 *                                          having an absolute value between
	 *                                          `lowerExp` and `upperExp` bounds, and
	 *                                          uses exponential notation elsewhere.
	 *                                          Lower bound is included, upper bound
	 *                                          is excluded.
	 *                                          For example '123.4' and '1.4e7'.
	 *                         'bin', 'oct, or
	 *                         'hex'            Format the number using binary, octal,
	 *                                          or hexadecimal notation.
	 *                                          For example '0b1101' and '0x10fe'.
	 *                     {number} wordSize    The word size in bits to use for formatting
	 *                                          in binary, octal, or hexadecimal notation.
	 *                                          To be used only with 'bin', 'oct', or 'hex'
	 *                                          values for 'notation' option. When this option
	 *                                          is defined the value is formatted as a signed
	 *                                          twos complement integer of the given word size
	 *                                          and the size suffix is appended to the output.
	 *                                          For example
	 *                                          format(-1, {notation: 'hex', wordSize: 8}) === '0xffi8'.
	 *                                          Default value is undefined.
	 *                     {number} precision   A number between 0 and 16 to round
	 *                                          the digits of the number.
	 *                                          In case of notations 'exponential',
	 *                                          'engineering', and 'auto',
	 *                                          `precision` defines the total
	 *                                          number of significant digits returned.
	 *                                          In case of notation 'fixed',
	 *                                          `precision` defines the number of
	 *                                          significant digits after the decimal
	 *                                          point.
	 *                                          `precision` is undefined by default,
	 *                                          not rounding any digits.
	 *                     {number} lowerExp    Exponent determining the lower boundary
	 *                                          for formatting a value with an exponent
	 *                                          when `notation='auto`.
	 *                                          Default value is `-3`.
	 *                     {number} upperExp    Exponent determining the upper boundary
	 *                                          for formatting a value with an exponent
	 *                                          when `notation='auto`.
	 *                                          Default value is `5`.
	 *    {Function} fn    A custom formatting function. Can be used to override the
	 *                     built-in notations. Function `fn` is called with `value` as
	 *                     parameter and must return a string. Is useful for example to
	 *                     format all values inside a matrix in a particular way.
	 *
	 * Examples:
	 *
	 *    format(6.4)                                        // '6.4'
	 *    format(1240000)                                    // '1.24e6'
	 *    format(1/3)                                        // '0.3333333333333333'
	 *    format(1/3, 3)                                     // '0.333'
	 *    format(21385, 2)                                   // '21000'
	 *    format(12.071, {notation: 'fixed'})                // '12'
	 *    format(2.3,    {notation: 'fixed', precision: 2})  // '2.30'
	 *    format(52.8,   {notation: 'exponential'})          // '5.28e+1'
	 *    format(12345678, {notation: 'engineering'})        // '12.345678e+6'
	 *
	 * @param {number} value
	 * @param {Object | Function | number} [options]
	 * @return {string} str The formatted value
	 */
	function format(value, options) {
	  if (typeof options === 'function') {
	    // handle format(value, fn)
	    return options(value);
	  }

	  // handle special cases
	  if (value === Infinity) {
	    return 'Infinity';
	  } else if (value === -Infinity) {
	    return '-Infinity';
	  } else if (isNaN(value)) {
	    return 'NaN';
	  }
	  const {
	    notation,
	    precision,
	    wordSize
	  } = normalizeFormatOptions(options);

	  // handle the various notations
	  switch (notation) {
	    case 'fixed':
	      return toFixed(value, precision);
	    case 'exponential':
	      return toExponential(value, precision);
	    case 'engineering':
	      return toEngineering(value, precision);
	    case 'bin':
	      return formatNumberToBase(value, 2, wordSize);
	    case 'oct':
	      return formatNumberToBase(value, 8, wordSize);
	    case 'hex':
	      return formatNumberToBase(value, 16, wordSize);
	    case 'auto':
	      // remove trailing zeros after the decimal point
	      return toPrecision(value, precision, options).replace(/((\.\d*?)(0+))($|e)/, function () {
	        const digits = arguments[2];
	        const e = arguments[4];
	        return digits !== '.' ? digits + e : e;
	      });
	    default:
	      throw new Error('Unknown notation "' + notation + '". ' + 'Choose "auto", "exponential", "fixed", "bin", "oct", or "hex.');
	  }
	}

	/**
	 * Normalize format options into an object:
	 *   {
	 *     notation: string,
	 *     precision: number | undefined,
	 *     wordSize: number | undefined
	 *   }
	 */
	function normalizeFormatOptions(options) {
	  // default values for options
	  let notation = 'auto';
	  let precision;
	  let wordSize;
	  if (options !== undefined) {
	    if ((0, _is.isNumber)(options)) {
	      precision = options;
	    } else if ((0, _is.isBigNumber)(options)) {
	      precision = options.toNumber();
	    } else if ((0, _is.isObject)(options)) {
	      if (options.precision !== undefined) {
	        precision = _toNumberOrThrow(options.precision, () => {
	          throw new Error('Option "precision" must be a number or BigNumber');
	        });
	      }
	      if (options.wordSize !== undefined) {
	        wordSize = _toNumberOrThrow(options.wordSize, () => {
	          throw new Error('Option "wordSize" must be a number or BigNumber');
	        });
	      }
	      if (options.notation) {
	        notation = options.notation;
	      }
	    } else {
	      throw new Error('Unsupported type of options, number, BigNumber, or object expected');
	    }
	  }
	  return {
	    notation,
	    precision,
	    wordSize
	  };
	}

	/**
	 * Split a number into sign, coefficients, and exponent
	 * @param {number | string} value
	 * @return {SplitValue}
	 *              Returns an object containing sign, coefficients, and exponent
	 */
	function splitNumber(value) {
	  // parse the input value
	  const match = String(value).toLowerCase().match(/^(-?)(\d+\.?\d*)(e([+-]?\d+))?$/);
	  if (!match) {
	    throw new SyntaxError('Invalid number ' + value);
	  }
	  const sign = match[1];
	  const digits = match[2];
	  let exponent = parseFloat(match[4] || '0');
	  const dot = digits.indexOf('.');
	  exponent += dot !== -1 ? dot - 1 : digits.length - 1;
	  const coefficients = digits.replace('.', '') // remove the dot (must be removed before removing leading zeros)
	  .replace(/^0*/, function (zeros) {
	    // remove leading zeros, add their count to the exponent
	    exponent -= zeros.length;
	    return '';
	  }).replace(/0*$/, '') // remove trailing zeros
	  .split('').map(function (d) {
	    return parseInt(d);
	  });
	  if (coefficients.length === 0) {
	    coefficients.push(0);
	    exponent++;
	  }
	  return {
	    sign,
	    coefficients,
	    exponent
	  };
	}

	/**
	 * Format a number in engineering notation. Like '1.23e+6', '2.3e+0', '3.500e-3'
	 * @param {number | string} value
	 * @param {number} [precision]        Optional number of significant figures to return.
	 */
	function toEngineering(value, precision) {
	  if (isNaN(value) || !Number.isFinite(value)) {
	    return String(value);
	  }
	  const split = splitNumber(value);
	  const rounded = roundDigits(split, precision);
	  const e = rounded.exponent;
	  const c = rounded.coefficients;

	  // find nearest lower multiple of 3 for exponent
	  const newExp = e % 3 === 0 ? e : e < 0 ? e - 3 - e % 3 : e - e % 3;
	  if ((0, _is.isNumber)(precision)) {
	    // add zeroes to give correct sig figs
	    while (precision > c.length || e - newExp + 1 > c.length) {
	      c.push(0);
	    }
	  } else {
	    // concatenate coefficients with necessary zeros
	    // add zeros if necessary (for example: 1e+8 -> 100e+6)
	    const missingZeros = Math.abs(e - newExp) - (c.length - 1);
	    for (let i = 0; i < missingZeros; i++) {
	      c.push(0);
	    }
	  }

	  // find difference in exponents
	  let expDiff = Math.abs(e - newExp);
	  let decimalIdx = 1;

	  // push decimal index over by expDiff times
	  while (expDiff > 0) {
	    decimalIdx++;
	    expDiff--;
	  }

	  // if all coefficient values are zero after the decimal point and precision is unset, don't add a decimal value.
	  // otherwise concat with the rest of the coefficients
	  const decimals = c.slice(decimalIdx).join('');
	  const decimalVal = (0, _is.isNumber)(precision) && decimals.length || decimals.match(/[1-9]/) ? '.' + decimals : '';
	  const str = c.slice(0, decimalIdx).join('') + decimalVal + 'e' + (e >= 0 ? '+' : '') + newExp.toString();
	  return rounded.sign + str;
	}

	/**
	 * Format a number with fixed notation.
	 * @param {number | string} value
	 * @param {number} [precision=undefined]  Optional number of decimals after the
	 *                                        decimal point. null by default.
	 */
	function toFixed(value, precision) {
	  if (isNaN(value) || !Number.isFinite(value)) {
	    return String(value);
	  }
	  const splitValue = splitNumber(value);
	  const rounded = typeof precision === 'number' ? roundDigits(splitValue, splitValue.exponent + 1 + precision) : splitValue;
	  let c = rounded.coefficients;
	  let p = rounded.exponent + 1; // exponent may have changed

	  // append zeros if needed
	  const pp = p + (precision || 0);
	  if (c.length < pp) {
	    c = c.concat(zeros(pp - c.length));
	  }

	  // prepend zeros if needed
	  if (p < 0) {
	    c = zeros(-p + 1).concat(c);
	    p = 1;
	  }

	  // insert a dot if needed
	  if (p < c.length) {
	    c.splice(p, 0, p === 0 ? '0.' : '.');
	  }
	  return rounded.sign + c.join('');
	}

	/**
	 * Format a number in exponential notation. Like '1.23e+5', '2.3e+0', '3.500e-3'
	 * @param {number | string} value
	 * @param {number} [precision]  Number of digits in formatted output.
	 *                              If not provided, the maximum available digits
	 *                              is used.
	 */
	function toExponential(value, precision) {
	  if (isNaN(value) || !Number.isFinite(Number(value))) {
	    return String(value);
	  }

	  // round if needed, else create a clone
	  const split = splitNumber(value);
	  const rounded = precision ? roundDigits(split, precision) : split;
	  let c = rounded.coefficients;
	  const e = rounded.exponent;

	  // append zeros if needed
	  if (c.length < precision) {
	    c = c.concat(zeros(precision - c.length));
	  }

	  // format as `C.CCCe+EEE` or `C.CCCe-EEE`
	  const first = c.shift();
	  return rounded.sign + first + (c.length > 0 ? '.' + c.join('') : '') + 'e' + (e >= 0 ? '+' : '') + e;
	}

	/**
	 * Format a number with a certain precision
	 * @param {number | string} value
	 * @param {number} [precision=undefined] Optional number of digits.
	 * @param {{lowerExp: number | undefined, upperExp: number | undefined}} [options]
	 *                                       By default:
	 *                                         lowerExp = -3 (incl)
	 *                                         upper = +5 (excl)
	 * @return {string}
	 */
	function toPrecision(value, precision, options) {
	  if (isNaN(value) || !Number.isFinite(value)) {
	    return String(value);
	  }

	  // determine lower and upper bound for exponential notation.
	  const lowerExp = _toNumberOrDefault(options === null || options === void 0 ? void 0 : options.lowerExp, -3);
	  const upperExp = _toNumberOrDefault(options === null || options === void 0 ? void 0 : options.upperExp, 5);
	  const split = splitNumber(value);
	  const rounded = precision ? roundDigits(split, precision) : split;
	  if (rounded.exponent < lowerExp || rounded.exponent >= upperExp) {
	    // exponential notation
	    return toExponential(value, precision);
	  } else {
	    let c = rounded.coefficients;
	    const e = rounded.exponent;

	    // append trailing zeros
	    if (c.length < precision) {
	      c = c.concat(zeros(precision - c.length));
	    }

	    // append trailing zeros
	    // TODO: simplify the next statement
	    c = c.concat(zeros(e - c.length + 1 + (c.length < precision ? precision - c.length : 0)));

	    // prepend zeros
	    c = zeros(-e).concat(c);
	    const dot = e > 0 ? e : 0;
	    if (dot < c.length - 1) {
	      c.splice(dot + 1, 0, '.');
	    }
	    return rounded.sign + c.join('');
	  }
	}

	/**
	 * Round the number of digits of a number *
	 * @param {SplitValue} split       A value split with .splitNumber(value)
	 * @param {number} precision  A positive integer
	 * @return {SplitValue}
	 *              Returns an object containing sign, coefficients, and exponent
	 *              with rounded digits
	 */
	function roundDigits(split, precision) {
	  // create a clone
	  const rounded = {
	    sign: split.sign,
	    coefficients: split.coefficients,
	    exponent: split.exponent
	  };
	  const c = rounded.coefficients;

	  // prepend zeros if needed
	  while (precision <= 0) {
	    c.unshift(0);
	    rounded.exponent++;
	    precision++;
	  }
	  if (c.length > precision) {
	    const removed = c.splice(precision, c.length - precision);
	    if (removed[0] >= 5) {
	      let i = precision - 1;
	      c[i]++;
	      while (c[i] === 10) {
	        c.pop();
	        if (i === 0) {
	          c.unshift(0);
	          rounded.exponent++;
	          i++;
	        }
	        i--;
	        c[i]++;
	      }
	    }
	  }
	  return rounded;
	}

	/**
	 * Create an array filled with zeros.
	 * @param {number} length
	 * @return {Array}
	 */
	function zeros(length) {
	  const arr = [];
	  for (let i = 0; i < length; i++) {
	    arr.push(0);
	  }
	  return arr;
	}

	/**
	 * Count the number of significant digits of a number.
	 *
	 * For example:
	 *   2.34 returns 3
	 *   0.0034 returns 2
	 *   120.5e+30 returns 4
	 *
	 * @param {number} value
	 * @return {number} digits   Number of significant digits
	 */
	function digits(value) {
	  return value.toExponential().replace(/e.*$/, '') // remove exponential notation
	  .replace(/^0\.?0*|\./, '') // remove decimal point and leading zeros
	  .length;
	}

	/**
	 * Compares two floating point numbers.
	 * @param {number} a - First value to compare
	 * @param {number} b - Second value to compare
	 * @param {number} [relTol=1e-09] - The relative tolerance, indicating the maximum allowed difference relative to the larger absolute value. Must be greater than 0.
	 * @param {number} [absTol=1e-12] - The minimum absolute tolerance, useful for comparisons near zero. Must be at least 0.
	 * @return {boolean} whether the two numbers are nearly equal
	 *
	 * @throws {Error} If `relTol` is less than or equal to 0.
	 * @throws {Error} If `absTol` is less than 0.
	 *
	 * @example
	 * nearlyEqual(1.000000001, 1.0, 1e-8);            // true
	 * nearlyEqual(1.000000002, 1.0, 0);            // false
	 * nearlyEqual(1.0, 1.009, undefined, 0.01);       // true
	 * nearlyEqual(0.000000001, 0.0, undefined, 1e-8); // true
	 */
	function nearlyEqual(a, b) {
	  let relTol = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1e-8;
	  let absTol = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
	  if (relTol <= 0) {
	    throw new Error('Relative tolerance must be greater than 0');
	  }
	  if (absTol < 0) {
	    throw new Error('Absolute tolerance must be at least 0');
	  }

	  // NaN
	  if (isNaN(a) || isNaN(b)) {
	    return false;
	  }
	  if (!Number.isFinite(a) || !Number.isFinite(b)) {
	    return a === b;
	  }
	  if (a === b) {
	    return true;
	  }

	  // abs(a-b) <= max(rel_tol * max(abs(a), abs(b)), abs_tol)
	  return Math.abs(a - b) <= Math.max(relTol * Math.max(Math.abs(a), Math.abs(b)), absTol);
	}

	/**
	 * Calculate the hyperbolic arccos of a number
	 * @param {number} x
	 * @return {number}
	 */
	number$3.acosh = Math.acosh || function (x) {
	  return Math.log(Math.sqrt(x * x - 1) + x);
	};
	number$3.asinh = Math.asinh || function (x) {
	  return Math.log(Math.sqrt(x * x + 1) + x);
	};

	/**
	 * Calculate the hyperbolic arctangent of a number
	 * @param {number} x
	 * @return {number}
	 */
	number$3.atanh = Math.atanh || function (x) {
	  return Math.log((1 + x) / (1 - x)) / 2;
	};

	/**
	 * Calculate the hyperbolic cosine of a number
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.cosh = Math.cosh || function (x) {
	  return (Math.exp(x) + Math.exp(-x)) / 2;
	};

	/**
	 * Calculate the hyperbolic sine of a number
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.sinh = Math.sinh || function (x) {
	  return (Math.exp(x) - Math.exp(-x)) / 2;
	};

	/**
	 * Calculate the hyperbolic tangent of a number
	 * @param {number} x
	 * @returns {number}
	 */
	number$3.tanh = Math.tanh || function (x) {
	  const e = Math.exp(2 * x);
	  return (e - 1) / (e + 1);
	};

	/**
	 * Returns a value with the magnitude of x and the sign of y.
	 * @param {number} x
	 * @param {number} y
	 * @returns {number}
	 */
	function copysign(x, y) {
	  const signx = x > 0 ? true : x < 0 ? false : 1 / x === Infinity;
	  const signy = y > 0 ? true : y < 0 ? false : 1 / y === Infinity;
	  return signx ^ signy ? -x : x;
	}
	function _toNumberOrThrow(value, onError) {
	  if ((0, _is.isNumber)(value)) {
	    return value;
	  } else if ((0, _is.isBigNumber)(value)) {
	    return value.toNumber();
	  } else {
	    onError();
	  }
	}
	function _toNumberOrDefault(value, defaultValue) {
	  if ((0, _is.isNumber)(value)) {
	    return value;
	  } else if ((0, _is.isBigNumber)(value)) {
	    return value.toNumber();
	  } else {
	    return defaultValue;
	  }
	}
	return number$3;
}

var hasRequiredTyped$1;

function requireTyped$1 () {
	if (hasRequiredTyped$1) return typed$1;
	hasRequiredTyped$1 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(typed$1, "__esModule", {
	  value: true
	});
	typed$1.createTyped = void 0;
	var _typedFunction = _interopRequireDefault(require$$2$1);
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _number = requireNumber$3();
	/**
	 * Create a typed-function which checks the types of the arguments and
	 * can match them against multiple provided signatures. The typed-function
	 * automatically converts inputs in order to find a matching signature.
	 * Typed functions throw informative errors in case of wrong input arguments.
	 *
	 * See the library [typed-function](https://github.com/josdejong/typed-function)
	 * for detailed documentation.
	 *
	 * Syntax:
	 *
	 *     math.typed(name, signatures) : function
	 *     math.typed(signatures) : function
	 *
	 * Examples:
	 *
	 *     // create a typed function with multiple types per argument (type union)
	 *     const fn2 = typed({
	 *       'number | boolean': function (b) {
	 *         return 'b is a number or boolean'
	 *       },
	 *       'string, number | boolean': function (a, b) {
	 *         return 'a is a string, b is a number or boolean'
	 *       }
	 *     })
	 *
	 *     // create a typed function with an any type argument
	 *     const log = typed({
	 *       'string, any': function (event, data) {
	 *         console.log('event: ' + event + ', data: ' + JSON.stringify(data))
	 *       }
	 *     })
	 *
	 * @param {string} [name]                          Optional name for the typed-function
	 * @param {Object<string, function>} signatures   Object with one or multiple function signatures
	 * @returns {function} The created typed-function.
	 */

	// returns a new instance of typed-function
	let _createTyped = function () {
	  // initially, return the original instance of typed-function
	  // consecutively, return a new instance from typed.create.
	  _createTyped = _typedFunction.default.create;
	  return _typedFunction.default;
	};
	const dependencies = ['?BigNumber', '?Complex', '?DenseMatrix', '?Fraction'];

	/**
	 * Factory function for creating a new typed instance
	 * @param {Object} dependencies   Object with data types like Complex and BigNumber
	 * @returns {Function}
	 */
	typed$1.createTyped = /* #__PURE__ */(0, _factory.factory)('typed', dependencies, function createTyped(_ref) {
	  let {
	    BigNumber,
	    Complex,
	    DenseMatrix,
	    Fraction
	  } = _ref;
	  // TODO: typed-function must be able to silently ignore signatures with unknown data types

	  // get a new instance of typed-function
	  const typed = _createTyped();

	  // define all types. The order of the types determines in which order function
	  // arguments are type-checked (so for performance it's important to put the
	  // most used types first).
	  typed.clear();
	  typed.addTypes([{
	    name: 'number',
	    test: _is.isNumber
	  }, {
	    name: 'Complex',
	    test: _is.isComplex
	  }, {
	    name: 'BigNumber',
	    test: _is.isBigNumber
	  }, {
	    name: 'bigint',
	    test: _is.isBigInt
	  }, {
	    name: 'Fraction',
	    test: _is.isFraction
	  }, {
	    name: 'Unit',
	    test: _is.isUnit
	  },
	  // The following type matches a valid variable name, i.e., an alphanumeric
	  // string starting with an alphabetic character. It is used (at least)
	  // in the definition of the derivative() function, as the argument telling
	  // what to differentiate over must (currently) be a variable.
	  // TODO: deprecate the identifier type (it's not used anymore, see https://github.com/josdejong/mathjs/issues/3253)
	  {
	    name: 'identifier',
	    test: s => _is.isString && /^[A-Za-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u0870-\u0887\u0889-\u088F\u08A0-\u08C9\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C5C\u0C5D\u0C60\u0C61\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDC-\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D04-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u1711\u171F-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4C\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C8A\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7DC\uA7F1-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB69\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC\u{10000}-\u{1000B}\u{1000D}-\u{10026}\u{10028}-\u{1003A}\u{1003C}\u{1003D}\u{1003F}-\u{1004D}\u{10050}-\u{1005D}\u{10080}-\u{100FA}\u{10280}-\u{1029C}\u{102A0}-\u{102D0}\u{10300}-\u{1031F}\u{1032D}-\u{10340}\u{10342}-\u{10349}\u{10350}-\u{10375}\u{10380}-\u{1039D}\u{103A0}-\u{103C3}\u{103C8}-\u{103CF}\u{10400}-\u{1049D}\u{104B0}-\u{104D3}\u{104D8}-\u{104FB}\u{10500}-\u{10527}\u{10530}-\u{10563}\u{10570}-\u{1057A}\u{1057C}-\u{1058A}\u{1058C}-\u{10592}\u{10594}\u{10595}\u{10597}-\u{105A1}\u{105A3}-\u{105B1}\u{105B3}-\u{105B9}\u{105BB}\u{105BC}\u{105C0}-\u{105F3}\u{10600}-\u{10736}\u{10740}-\u{10755}\u{10760}-\u{10767}\u{10780}-\u{10785}\u{10787}-\u{107B0}\u{107B2}-\u{107BA}\u{10800}-\u{10805}\u{10808}\u{1080A}-\u{10835}\u{10837}\u{10838}\u{1083C}\u{1083F}-\u{10855}\u{10860}-\u{10876}\u{10880}-\u{1089E}\u{108E0}-\u{108F2}\u{108F4}\u{108F5}\u{10900}-\u{10915}\u{10920}-\u{10939}\u{10940}-\u{10959}\u{10980}-\u{109B7}\u{109BE}\u{109BF}\u{10A00}\u{10A10}-\u{10A13}\u{10A15}-\u{10A17}\u{10A19}-\u{10A35}\u{10A60}-\u{10A7C}\u{10A80}-\u{10A9C}\u{10AC0}-\u{10AC7}\u{10AC9}-\u{10AE4}\u{10B00}-\u{10B35}\u{10B40}-\u{10B55}\u{10B60}-\u{10B72}\u{10B80}-\u{10B91}\u{10C00}-\u{10C48}\u{10C80}-\u{10CB2}\u{10CC0}-\u{10CF2}\u{10D00}-\u{10D23}\u{10D4A}-\u{10D65}\u{10D6F}-\u{10D85}\u{10E80}-\u{10EA9}\u{10EB0}\u{10EB1}\u{10EC2}-\u{10EC7}\u{10F00}-\u{10F1C}\u{10F27}\u{10F30}-\u{10F45}\u{10F70}-\u{10F81}\u{10FB0}-\u{10FC4}\u{10FE0}-\u{10FF6}\u{11003}-\u{11037}\u{11071}\u{11072}\u{11075}\u{11083}-\u{110AF}\u{110D0}-\u{110E8}\u{11103}-\u{11126}\u{11144}\u{11147}\u{11150}-\u{11172}\u{11176}\u{11183}-\u{111B2}\u{111C1}-\u{111C4}\u{111DA}\u{111DC}\u{11200}-\u{11211}\u{11213}-\u{1122B}\u{1123F}\u{11240}\u{11280}-\u{11286}\u{11288}\u{1128A}-\u{1128D}\u{1128F}-\u{1129D}\u{1129F}-\u{112A8}\u{112B0}-\u{112DE}\u{11305}-\u{1130C}\u{1130F}\u{11310}\u{11313}-\u{11328}\u{1132A}-\u{11330}\u{11332}\u{11333}\u{11335}-\u{11339}\u{1133D}\u{11350}\u{1135D}-\u{11361}\u{11380}-\u{11389}\u{1138B}\u{1138E}\u{11390}-\u{113B5}\u{113B7}\u{113D1}\u{113D3}\u{11400}-\u{11434}\u{11447}-\u{1144A}\u{1145F}-\u{11461}\u{11480}-\u{114AF}\u{114C4}\u{114C5}\u{114C7}\u{11580}-\u{115AE}\u{115D8}-\u{115DB}\u{11600}-\u{1162F}\u{11644}\u{11680}-\u{116AA}\u{116B8}\u{11700}-\u{1171A}\u{11740}-\u{11746}\u{11800}-\u{1182B}\u{118A0}-\u{118DF}\u{118FF}-\u{11906}\u{11909}\u{1190C}-\u{11913}\u{11915}\u{11916}\u{11918}-\u{1192F}\u{1193F}\u{11941}\u{119A0}-\u{119A7}\u{119AA}-\u{119D0}\u{119E1}\u{119E3}\u{11A00}\u{11A0B}-\u{11A32}\u{11A3A}\u{11A50}\u{11A5C}-\u{11A89}\u{11A9D}\u{11AB0}-\u{11AF8}\u{11BC0}-\u{11BE0}\u{11C00}-\u{11C08}\u{11C0A}-\u{11C2E}\u{11C40}\u{11C72}-\u{11C8F}\u{11D00}-\u{11D06}\u{11D08}\u{11D09}\u{11D0B}-\u{11D30}\u{11D46}\u{11D60}-\u{11D65}\u{11D67}\u{11D68}\u{11D6A}-\u{11D89}\u{11D98}\u{11DB0}-\u{11DDB}\u{11EE0}-\u{11EF2}\u{11F02}\u{11F04}-\u{11F10}\u{11F12}-\u{11F33}\u{11FB0}\u{12000}-\u{12399}\u{12480}-\u{12543}\u{12F90}-\u{12FF0}\u{13000}-\u{1342F}\u{13441}-\u{13446}\u{13460}-\u{143FA}\u{14400}-\u{14646}\u{16100}-\u{1611D}\u{16800}-\u{16A38}\u{16A40}-\u{16A5E}\u{16A70}-\u{16ABE}\u{16AD0}-\u{16AED}\u{16B00}-\u{16B2F}\u{16B40}-\u{16B43}\u{16B63}-\u{16B77}\u{16B7D}-\u{16B8F}\u{16D40}-\u{16D6C}\u{16E40}-\u{16E7F}\u{16EA0}-\u{16EB8}\u{16EBB}-\u{16ED3}\u{16F00}-\u{16F4A}\u{16F50}\u{16F93}-\u{16F9F}\u{16FE0}\u{16FE1}\u{16FE3}\u{16FF2}\u{16FF3}\u{17000}-\u{18CD5}\u{18CFF}-\u{18D1E}\u{18D80}-\u{18DF2}\u{1AFF0}-\u{1AFF3}\u{1AFF5}-\u{1AFFB}\u{1AFFD}\u{1AFFE}\u{1B000}-\u{1B122}\u{1B132}\u{1B150}-\u{1B152}\u{1B155}\u{1B164}-\u{1B167}\u{1B170}-\u{1B2FB}\u{1BC00}-\u{1BC6A}\u{1BC70}-\u{1BC7C}\u{1BC80}-\u{1BC88}\u{1BC90}-\u{1BC99}\u{1D400}-\u{1D454}\u{1D456}-\u{1D49C}\u{1D49E}\u{1D49F}\u{1D4A2}\u{1D4A5}\u{1D4A6}\u{1D4A9}-\u{1D4AC}\u{1D4AE}-\u{1D4B9}\u{1D4BB}\u{1D4BD}-\u{1D4C3}\u{1D4C5}-\u{1D505}\u{1D507}-\u{1D50A}\u{1D50D}-\u{1D514}\u{1D516}-\u{1D51C}\u{1D51E}-\u{1D539}\u{1D53B}-\u{1D53E}\u{1D540}-\u{1D544}\u{1D546}\u{1D54A}-\u{1D550}\u{1D552}-\u{1D6A5}\u{1D6A8}-\u{1D6C0}\u{1D6C2}-\u{1D6DA}\u{1D6DC}-\u{1D6FA}\u{1D6FC}-\u{1D714}\u{1D716}-\u{1D734}\u{1D736}-\u{1D74E}\u{1D750}-\u{1D76E}\u{1D770}-\u{1D788}\u{1D78A}-\u{1D7A8}\u{1D7AA}-\u{1D7C2}\u{1D7C4}-\u{1D7CB}\u{1DF00}-\u{1DF1E}\u{1DF25}-\u{1DF2A}\u{1E030}-\u{1E06D}\u{1E100}-\u{1E12C}\u{1E137}-\u{1E13D}\u{1E14E}\u{1E290}-\u{1E2AD}\u{1E2C0}-\u{1E2EB}\u{1E4D0}-\u{1E4EB}\u{1E5D0}-\u{1E5ED}\u{1E5F0}\u{1E6C0}-\u{1E6DE}\u{1E6E0}-\u{1E6E2}\u{1E6E4}\u{1E6E5}\u{1E6E7}-\u{1E6ED}\u{1E6F0}-\u{1E6F4}\u{1E6FE}\u{1E6FF}\u{1E7E0}-\u{1E7E6}\u{1E7E8}-\u{1E7EB}\u{1E7ED}\u{1E7EE}\u{1E7F0}-\u{1E7FE}\u{1E800}-\u{1E8C4}\u{1E900}-\u{1E943}\u{1E94B}\u{1EE00}-\u{1EE03}\u{1EE05}-\u{1EE1F}\u{1EE21}\u{1EE22}\u{1EE24}\u{1EE27}\u{1EE29}-\u{1EE32}\u{1EE34}-\u{1EE37}\u{1EE39}\u{1EE3B}\u{1EE42}\u{1EE47}\u{1EE49}\u{1EE4B}\u{1EE4D}-\u{1EE4F}\u{1EE51}\u{1EE52}\u{1EE54}\u{1EE57}\u{1EE59}\u{1EE5B}\u{1EE5D}\u{1EE5F}\u{1EE61}\u{1EE62}\u{1EE64}\u{1EE67}-\u{1EE6A}\u{1EE6C}-\u{1EE72}\u{1EE74}-\u{1EE77}\u{1EE79}-\u{1EE7C}\u{1EE7E}\u{1EE80}-\u{1EE89}\u{1EE8B}-\u{1EE9B}\u{1EEA1}-\u{1EEA3}\u{1EEA5}-\u{1EEA9}\u{1EEAB}-\u{1EEBB}\u{20000}-\u{2A6DF}\u{2A700}-\u{2B81D}\u{2B820}-\u{2CEAD}\u{2CEB0}-\u{2EBE0}\u{2EBF0}-\u{2EE5D}\u{2F800}-\u{2FA1D}\u{30000}-\u{3134A}\u{31350}-\u{33479}][0-9A-Za-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u0870-\u0887\u0889-\u088F\u08A0-\u08C9\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C5C\u0C5D\u0C60\u0C61\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDC-\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D04-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u1711\u171F-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4C\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C8A\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7DC\uA7F1-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB69\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC\u{10000}-\u{1000B}\u{1000D}-\u{10026}\u{10028}-\u{1003A}\u{1003C}\u{1003D}\u{1003F}-\u{1004D}\u{10050}-\u{1005D}\u{10080}-\u{100FA}\u{10280}-\u{1029C}\u{102A0}-\u{102D0}\u{10300}-\u{1031F}\u{1032D}-\u{10340}\u{10342}-\u{10349}\u{10350}-\u{10375}\u{10380}-\u{1039D}\u{103A0}-\u{103C3}\u{103C8}-\u{103CF}\u{10400}-\u{1049D}\u{104B0}-\u{104D3}\u{104D8}-\u{104FB}\u{10500}-\u{10527}\u{10530}-\u{10563}\u{10570}-\u{1057A}\u{1057C}-\u{1058A}\u{1058C}-\u{10592}\u{10594}\u{10595}\u{10597}-\u{105A1}\u{105A3}-\u{105B1}\u{105B3}-\u{105B9}\u{105BB}\u{105BC}\u{105C0}-\u{105F3}\u{10600}-\u{10736}\u{10740}-\u{10755}\u{10760}-\u{10767}\u{10780}-\u{10785}\u{10787}-\u{107B0}\u{107B2}-\u{107BA}\u{10800}-\u{10805}\u{10808}\u{1080A}-\u{10835}\u{10837}\u{10838}\u{1083C}\u{1083F}-\u{10855}\u{10860}-\u{10876}\u{10880}-\u{1089E}\u{108E0}-\u{108F2}\u{108F4}\u{108F5}\u{10900}-\u{10915}\u{10920}-\u{10939}\u{10940}-\u{10959}\u{10980}-\u{109B7}\u{109BE}\u{109BF}\u{10A00}\u{10A10}-\u{10A13}\u{10A15}-\u{10A17}\u{10A19}-\u{10A35}\u{10A60}-\u{10A7C}\u{10A80}-\u{10A9C}\u{10AC0}-\u{10AC7}\u{10AC9}-\u{10AE4}\u{10B00}-\u{10B35}\u{10B40}-\u{10B55}\u{10B60}-\u{10B72}\u{10B80}-\u{10B91}\u{10C00}-\u{10C48}\u{10C80}-\u{10CB2}\u{10CC0}-\u{10CF2}\u{10D00}-\u{10D23}\u{10D4A}-\u{10D65}\u{10D6F}-\u{10D85}\u{10E80}-\u{10EA9}\u{10EB0}\u{10EB1}\u{10EC2}-\u{10EC7}\u{10F00}-\u{10F1C}\u{10F27}\u{10F30}-\u{10F45}\u{10F70}-\u{10F81}\u{10FB0}-\u{10FC4}\u{10FE0}-\u{10FF6}\u{11003}-\u{11037}\u{11071}\u{11072}\u{11075}\u{11083}-\u{110AF}\u{110D0}-\u{110E8}\u{11103}-\u{11126}\u{11144}\u{11147}\u{11150}-\u{11172}\u{11176}\u{11183}-\u{111B2}\u{111C1}-\u{111C4}\u{111DA}\u{111DC}\u{11200}-\u{11211}\u{11213}-\u{1122B}\u{1123F}\u{11240}\u{11280}-\u{11286}\u{11288}\u{1128A}-\u{1128D}\u{1128F}-\u{1129D}\u{1129F}-\u{112A8}\u{112B0}-\u{112DE}\u{11305}-\u{1130C}\u{1130F}\u{11310}\u{11313}-\u{11328}\u{1132A}-\u{11330}\u{11332}\u{11333}\u{11335}-\u{11339}\u{1133D}\u{11350}\u{1135D}-\u{11361}\u{11380}-\u{11389}\u{1138B}\u{1138E}\u{11390}-\u{113B5}\u{113B7}\u{113D1}\u{113D3}\u{11400}-\u{11434}\u{11447}-\u{1144A}\u{1145F}-\u{11461}\u{11480}-\u{114AF}\u{114C4}\u{114C5}\u{114C7}\u{11580}-\u{115AE}\u{115D8}-\u{115DB}\u{11600}-\u{1162F}\u{11644}\u{11680}-\u{116AA}\u{116B8}\u{11700}-\u{1171A}\u{11740}-\u{11746}\u{11800}-\u{1182B}\u{118A0}-\u{118DF}\u{118FF}-\u{11906}\u{11909}\u{1190C}-\u{11913}\u{11915}\u{11916}\u{11918}-\u{1192F}\u{1193F}\u{11941}\u{119A0}-\u{119A7}\u{119AA}-\u{119D0}\u{119E1}\u{119E3}\u{11A00}\u{11A0B}-\u{11A32}\u{11A3A}\u{11A50}\u{11A5C}-\u{11A89}\u{11A9D}\u{11AB0}-\u{11AF8}\u{11BC0}-\u{11BE0}\u{11C00}-\u{11C08}\u{11C0A}-\u{11C2E}\u{11C40}\u{11C72}-\u{11C8F}\u{11D00}-\u{11D06}\u{11D08}\u{11D09}\u{11D0B}-\u{11D30}\u{11D46}\u{11D60}-\u{11D65}\u{11D67}\u{11D68}\u{11D6A}-\u{11D89}\u{11D98}\u{11DB0}-\u{11DDB}\u{11EE0}-\u{11EF2}\u{11F02}\u{11F04}-\u{11F10}\u{11F12}-\u{11F33}\u{11FB0}\u{12000}-\u{12399}\u{12480}-\u{12543}\u{12F90}-\u{12FF0}\u{13000}-\u{1342F}\u{13441}-\u{13446}\u{13460}-\u{143FA}\u{14400}-\u{14646}\u{16100}-\u{1611D}\u{16800}-\u{16A38}\u{16A40}-\u{16A5E}\u{16A70}-\u{16ABE}\u{16AD0}-\u{16AED}\u{16B00}-\u{16B2F}\u{16B40}-\u{16B43}\u{16B63}-\u{16B77}\u{16B7D}-\u{16B8F}\u{16D40}-\u{16D6C}\u{16E40}-\u{16E7F}\u{16EA0}-\u{16EB8}\u{16EBB}-\u{16ED3}\u{16F00}-\u{16F4A}\u{16F50}\u{16F93}-\u{16F9F}\u{16FE0}\u{16FE1}\u{16FE3}\u{16FF2}\u{16FF3}\u{17000}-\u{18CD5}\u{18CFF}-\u{18D1E}\u{18D80}-\u{18DF2}\u{1AFF0}-\u{1AFF3}\u{1AFF5}-\u{1AFFB}\u{1AFFD}\u{1AFFE}\u{1B000}-\u{1B122}\u{1B132}\u{1B150}-\u{1B152}\u{1B155}\u{1B164}-\u{1B167}\u{1B170}-\u{1B2FB}\u{1BC00}-\u{1BC6A}\u{1BC70}-\u{1BC7C}\u{1BC80}-\u{1BC88}\u{1BC90}-\u{1BC99}\u{1D400}-\u{1D454}\u{1D456}-\u{1D49C}\u{1D49E}\u{1D49F}\u{1D4A2}\u{1D4A5}\u{1D4A6}\u{1D4A9}-\u{1D4AC}\u{1D4AE}-\u{1D4B9}\u{1D4BB}\u{1D4BD}-\u{1D4C3}\u{1D4C5}-\u{1D505}\u{1D507}-\u{1D50A}\u{1D50D}-\u{1D514}\u{1D516}-\u{1D51C}\u{1D51E}-\u{1D539}\u{1D53B}-\u{1D53E}\u{1D540}-\u{1D544}\u{1D546}\u{1D54A}-\u{1D550}\u{1D552}-\u{1D6A5}\u{1D6A8}-\u{1D6C0}\u{1D6C2}-\u{1D6DA}\u{1D6DC}-\u{1D6FA}\u{1D6FC}-\u{1D714}\u{1D716}-\u{1D734}\u{1D736}-\u{1D74E}\u{1D750}-\u{1D76E}\u{1D770}-\u{1D788}\u{1D78A}-\u{1D7A8}\u{1D7AA}-\u{1D7C2}\u{1D7C4}-\u{1D7CB}\u{1DF00}-\u{1DF1E}\u{1DF25}-\u{1DF2A}\u{1E030}-\u{1E06D}\u{1E100}-\u{1E12C}\u{1E137}-\u{1E13D}\u{1E14E}\u{1E290}-\u{1E2AD}\u{1E2C0}-\u{1E2EB}\u{1E4D0}-\u{1E4EB}\u{1E5D0}-\u{1E5ED}\u{1E5F0}\u{1E6C0}-\u{1E6DE}\u{1E6E0}-\u{1E6E2}\u{1E6E4}\u{1E6E5}\u{1E6E7}-\u{1E6ED}\u{1E6F0}-\u{1E6F4}\u{1E6FE}\u{1E6FF}\u{1E7E0}-\u{1E7E6}\u{1E7E8}-\u{1E7EB}\u{1E7ED}\u{1E7EE}\u{1E7F0}-\u{1E7FE}\u{1E800}-\u{1E8C4}\u{1E900}-\u{1E943}\u{1E94B}\u{1EE00}-\u{1EE03}\u{1EE05}-\u{1EE1F}\u{1EE21}\u{1EE22}\u{1EE24}\u{1EE27}\u{1EE29}-\u{1EE32}\u{1EE34}-\u{1EE37}\u{1EE39}\u{1EE3B}\u{1EE42}\u{1EE47}\u{1EE49}\u{1EE4B}\u{1EE4D}-\u{1EE4F}\u{1EE51}\u{1EE52}\u{1EE54}\u{1EE57}\u{1EE59}\u{1EE5B}\u{1EE5D}\u{1EE5F}\u{1EE61}\u{1EE62}\u{1EE64}\u{1EE67}-\u{1EE6A}\u{1EE6C}-\u{1EE72}\u{1EE74}-\u{1EE77}\u{1EE79}-\u{1EE7C}\u{1EE7E}\u{1EE80}-\u{1EE89}\u{1EE8B}-\u{1EE9B}\u{1EEA1}-\u{1EEA3}\u{1EEA5}-\u{1EEA9}\u{1EEAB}-\u{1EEBB}\u{20000}-\u{2A6DF}\u{2A700}-\u{2B81D}\u{2B820}-\u{2CEAD}\u{2CEB0}-\u{2EBE0}\u{2EBF0}-\u{2EE5D}\u{2F800}-\u{2FA1D}\u{30000}-\u{3134A}\u{31350}-\u{33479}]*$/u.test(s)
	  }, {
	    name: 'string',
	    test: _is.isString
	  }, {
	    name: 'Chain',
	    test: _is.isChain
	  }, {
	    name: 'Array',
	    test: _is.isArray
	  }, {
	    name: 'Matrix',
	    test: _is.isMatrix
	  }, {
	    name: 'DenseMatrix',
	    test: _is.isDenseMatrix
	  }, {
	    name: 'SparseMatrix',
	    test: _is.isSparseMatrix
	  }, {
	    name: 'Range',
	    test: _is.isRange
	  }, {
	    name: 'Index',
	    test: _is.isIndex
	  }, {
	    name: 'boolean',
	    test: _is.isBoolean
	  }, {
	    name: 'ResultSet',
	    test: _is.isResultSet
	  }, {
	    name: 'Help',
	    test: _is.isHelp
	  }, {
	    name: 'function',
	    test: _is.isFunction
	  }, {
	    name: 'Date',
	    test: _is.isDate
	  }, {
	    name: 'RegExp',
	    test: _is.isRegExp
	  }, {
	    name: 'null',
	    test: _is.isNull
	  }, {
	    name: 'undefined',
	    test: _is.isUndefined
	  }, {
	    name: 'AccessorNode',
	    test: _is.isAccessorNode
	  }, {
	    name: 'ArrayNode',
	    test: _is.isArrayNode
	  }, {
	    name: 'AssignmentNode',
	    test: _is.isAssignmentNode
	  }, {
	    name: 'BlockNode',
	    test: _is.isBlockNode
	  }, {
	    name: 'ConditionalNode',
	    test: _is.isConditionalNode
	  }, {
	    name: 'ConstantNode',
	    test: _is.isConstantNode
	  }, {
	    name: 'FunctionNode',
	    test: _is.isFunctionNode
	  }, {
	    name: 'FunctionAssignmentNode',
	    test: _is.isFunctionAssignmentNode
	  }, {
	    name: 'IndexNode',
	    test: _is.isIndexNode
	  }, {
	    name: 'Node',
	    test: _is.isNode
	  }, {
	    name: 'ObjectNode',
	    test: _is.isObjectNode
	  }, {
	    name: 'OperatorNode',
	    test: _is.isOperatorNode
	  }, {
	    name: 'ParenthesisNode',
	    test: _is.isParenthesisNode
	  }, {
	    name: 'RangeNode',
	    test: _is.isRangeNode
	  }, {
	    name: 'RelationalNode',
	    test: _is.isRelationalNode
	  }, {
	    name: 'SymbolNode',
	    test: _is.isSymbolNode
	  }, {
	    name: 'Map',
	    test: _is.isMap
	  }, {
	    name: 'Object',
	    test: _is.isObject
	  } // order 'Object' last, it matches on other classes too
	  ]);
	  typed.addConversions([{
	    from: 'number',
	    to: 'BigNumber',
	    convert: function (x) {
	      if (!BigNumber) {
	        throwNoBignumber(x);
	      }

	      // note: conversion from number to BigNumber can fail if x has >15 digits
	      if ((0, _number.digits)(x) > 15) {
	        throw new TypeError('Cannot implicitly convert a number with >15 significant digits to BigNumber ' + '(value: ' + x + '). ' + 'Use function bignumber(x) to convert to BigNumber.');
	      }
	      return new BigNumber(x);
	    }
	  }, {
	    from: 'number',
	    to: 'Complex',
	    convert: function (x) {
	      if (!Complex) {
	        throwNoComplex(x);
	      }
	      return new Complex(x, 0);
	    }
	  }, {
	    from: 'BigNumber',
	    to: 'Complex',
	    convert: function (x) {
	      if (!Complex) {
	        throwNoComplex(x);
	      }
	      return new Complex(x.toNumber(), 0);
	    }
	  }, {
	    from: 'bigint',
	    to: 'number',
	    convert: function (x) {
	      if (x > Number.MAX_SAFE_INTEGER) {
	        throw new TypeError('Cannot implicitly convert bigint to number: ' + 'value exceeds the max safe integer value (value: ' + x + ')');
	      }
	      return Number(x);
	    }
	  }, {
	    from: 'bigint',
	    to: 'BigNumber',
	    convert: function (x) {
	      if (!BigNumber) {
	        throwNoBignumber(x);
	      }
	      return new BigNumber(x.toString());
	    }
	  }, {
	    from: 'bigint',
	    to: 'Fraction',
	    convert: function (x) {
	      if (!Fraction) {
	        throwNoFraction(x);
	      }
	      return new Fraction(x);
	    }
	  }, {
	    from: 'Fraction',
	    to: 'BigNumber',
	    convert: function (x) {
	      throw new TypeError('Cannot implicitly convert a Fraction to BigNumber or vice versa. ' + 'Use function bignumber(x) to convert to BigNumber or fraction(x) to convert to Fraction.');
	    }
	  }, {
	    from: 'Fraction',
	    to: 'Complex',
	    convert: function (x) {
	      if (!Complex) {
	        throwNoComplex(x);
	      }
	      return new Complex(x.valueOf(), 0);
	    }
	  }, {
	    from: 'number',
	    to: 'Fraction',
	    convert: function (x) {
	      if (!Fraction) {
	        throwNoFraction(x);
	      }
	      const f = new Fraction(x);
	      if (f.valueOf() !== x) {
	        throw new TypeError('Cannot implicitly convert a number to a Fraction when there will be a loss of precision ' + '(value: ' + x + '). ' + 'Use function fraction(x) to convert to Fraction.');
	      }
	      return f;
	    }
	  }, {
	    // FIXME: add conversion from Fraction to number, for example for `sqrt(fraction(1,3))`
	    //  from: 'Fraction',
	    //  to: 'number',
	    //  convert: function (x) {
	    //    return x.valueOf()
	    //  }
	    // }, {
	    from: 'string',
	    to: 'number',
	    convert: function (x) {
	      const n = Number(x);
	      if (isNaN(n)) {
	        throw new Error('Cannot convert "' + x + '" to a number');
	      }
	      return n;
	    }
	  }, {
	    from: 'string',
	    to: 'BigNumber',
	    convert: function (x) {
	      if (!BigNumber) {
	        throwNoBignumber(x);
	      }
	      try {
	        return new BigNumber(x);
	      } catch (err) {
	        throw new Error('Cannot convert "' + x + '" to BigNumber');
	      }
	    }
	  }, {
	    from: 'string',
	    to: 'bigint',
	    convert: function (x) {
	      try {
	        return BigInt(x);
	      } catch (err) {
	        throw new Error('Cannot convert "' + x + '" to BigInt');
	      }
	    }
	  }, {
	    from: 'string',
	    to: 'Fraction',
	    convert: function (x) {
	      if (!Fraction) {
	        throwNoFraction(x);
	      }
	      try {
	        return new Fraction(x);
	      } catch (err) {
	        throw new Error('Cannot convert "' + x + '" to Fraction');
	      }
	    }
	  }, {
	    from: 'string',
	    to: 'Complex',
	    convert: function (x) {
	      if (!Complex) {
	        throwNoComplex(x);
	      }
	      try {
	        return new Complex(x);
	      } catch (err) {
	        throw new Error('Cannot convert "' + x + '" to Complex');
	      }
	    }
	  }, {
	    from: 'boolean',
	    to: 'number',
	    convert: function (x) {
	      return +x;
	    }
	  }, {
	    from: 'boolean',
	    to: 'BigNumber',
	    convert: function (x) {
	      if (!BigNumber) {
	        throwNoBignumber(x);
	      }
	      return new BigNumber(+x);
	    }
	  }, {
	    from: 'boolean',
	    to: 'bigint',
	    convert: function (x) {
	      return BigInt(+x);
	    }
	  }, {
	    from: 'boolean',
	    to: 'Fraction',
	    convert: function (x) {
	      if (!Fraction) {
	        throwNoFraction(x);
	      }
	      return new Fraction(+x);
	    }
	  }, {
	    from: 'boolean',
	    to: 'string',
	    convert: function (x) {
	      return String(x);
	    }
	  }, {
	    from: 'Array',
	    to: 'Matrix',
	    convert: function (array) {
	      if (!DenseMatrix) {
	        throwNoMatrix();
	      }
	      return new DenseMatrix(array);
	    }
	  }, {
	    from: 'Matrix',
	    to: 'Array',
	    convert: function (matrix) {
	      return matrix.valueOf();
	    }
	  }]);

	  // Provide a suggestion on how to call a function elementwise
	  // This was added primarily as guidance for the v10 -> v11 transition,
	  // and could potentially be removed in the future if it no longer seems
	  // to be helpful.
	  typed.onMismatch = (name, args, signatures) => {
	    const usualError = typed.createError(name, args, signatures);
	    if (['wrongType', 'mismatch'].includes(usualError.data.category) && args.length === 1 && (0, _is.isCollection)(args[0]) &&
	    // check if the function can be unary:
	    signatures.some(sig => !sig.params.includes(','))) {
	      const err = new TypeError(`Function '${name}' doesn't apply to matrices. To call it ` + `elementwise on a matrix 'M', try 'map(M, ${name})'.`);
	      err.data = usualError.data;
	      throw err;
	    }
	    throw usualError;
	  };

	  // Provide a suggestion on how to call a function elementwise
	  // This was added primarily as guidance for the v10 -> v11 transition,
	  // and could potentially be removed in the future if it no longer seems
	  // to be helpful.
	  typed.onMismatch = (name, args, signatures) => {
	    const usualError = typed.createError(name, args, signatures);
	    if (['wrongType', 'mismatch'].includes(usualError.data.category) && args.length === 1 && (0, _is.isCollection)(args[0]) &&
	    // check if the function can be unary:
	    signatures.some(sig => !sig.params.includes(','))) {
	      const err = new TypeError(`Function '${name}' doesn't apply to matrices. To call it ` + `elementwise on a matrix 'M', try 'map(M, ${name})'.`);
	      err.data = usualError.data;
	      throw err;
	    }
	    throw usualError;
	  };
	  return typed;
	});
	function throwNoBignumber(x) {
	  throw new Error(`Cannot convert value ${x} into a BigNumber: no class 'BigNumber' provided`);
	}
	function throwNoComplex(x) {
	  throw new Error(`Cannot convert value ${x} into a Complex number: no class 'Complex' provided`);
	}
	function throwNoMatrix() {
	  throw new Error('Cannot convert array into a Matrix: no class \'DenseMatrix\' provided');
	}
	function throwNoFraction(x) {
	  throw new Error(`Cannot convert value ${x} into a Fraction, no class 'Fraction' provided.`);
	}
	return typed$1;
}

var ResultSet = {};

var hasRequiredResultSet;

function requireResultSet () {
	if (hasRequiredResultSet) return ResultSet;
	hasRequiredResultSet = 1;

	Object.defineProperty(ResultSet, "__esModule", {
	  value: true
	});
	ResultSet.createResultSet = void 0;
	var _factory = requireFactory$b();
	const name = 'ResultSet';
	const dependencies = [];
	ResultSet.createResultSet = /* #__PURE__ */(0, _factory.factory)(name, dependencies, () => {
	  /**
	   * A ResultSet contains a list or results
	   * @class ResultSet
	   * @param {Array} entries
	   * @constructor ResultSet
	   */
	  function ResultSet(entries) {
	    if (!(this instanceof ResultSet)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    this.entries = entries || [];
	  }

	  /**
	   * Attach type information
	   */
	  ResultSet.prototype.type = 'ResultSet';
	  ResultSet.prototype.isResultSet = true;

	  /**
	   * Returns the array with results hold by this ResultSet
	   * @memberof ResultSet
	   * @returns {Array} entries
	   */
	  ResultSet.prototype.valueOf = function () {
	    return this.entries;
	  };

	  /**
	   * Returns the stringified results of the ResultSet
	   * @memberof ResultSet
	   * @returns {string} string
	   */
	  ResultSet.prototype.toString = function () {
	    return '[' + this.entries.map(String).join(', ') + ']';
	  };

	  /**
	   * Get a JSON representation of the ResultSet
	   * @memberof ResultSet
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "ResultSet", "entries": [...]}`
	   */
	  ResultSet.prototype.toJSON = function () {
	    return {
	      mathjs: 'ResultSet',
	      entries: this.entries
	    };
	  };

	  /**
	   * Instantiate a ResultSet from a JSON object
	   * @memberof ResultSet
	   * @param {Object} json  A JSON object structured as:
	   *                       `{"mathjs": "ResultSet", "entries": [...]}`
	   * @return {ResultSet}
	   */
	  ResultSet.fromJSON = function (json) {
	    return new ResultSet(json.entries);
	  };
	  return ResultSet;
	}, {
	  isClass: true
	});
	return ResultSet;
}

var BigNumber = {};

var decimal$1 = {exports: {}};

var decimal = decimal$1.exports;

var hasRequiredDecimal;

function requireDecimal () {
	if (hasRequiredDecimal) return decimal$1.exports;
	hasRequiredDecimal = 1;
	(function (module) {
(function (globalScope) {


		  /*!
		   *  decimal.js v10.6.0
		   *  An arbitrary-precision Decimal type for JavaScript.
		   *  https://github.com/MikeMcl/decimal.js
		   *  Copyright (c) 2025 Michael Mclaughlin <M8ch88l@gmail.com>
		   *  MIT Licence
		   */


		  // -----------------------------------  EDITABLE DEFAULTS  ------------------------------------ //


		    // The maximum exponent magnitude.
		    // The limit on the value of `toExpNeg`, `toExpPos`, `minE` and `maxE`.
		  var EXP_LIMIT = 9e15,                      // 0 to 9e15

		    // The limit on the value of `precision`, and on the value of the first argument to
		    // `toDecimalPlaces`, `toExponential`, `toFixed`, `toPrecision` and `toSignificantDigits`.
		    MAX_DIGITS = 1e9,                        // 0 to 1e9

		    // Base conversion alphabet.
		    NUMERALS = '0123456789abcdef',

		    // The natural logarithm of 10 (1025 digits).
		    LN10 = '2.3025850929940456840179914546843642076011014886287729760333279009675726096773524802359972050895982983419677840422862486334095254650828067566662873690987816894829072083255546808437998948262331985283935053089653777326288461633662222876982198867465436674744042432743651550489343149393914796194044002221051017141748003688084012647080685567743216228355220114804663715659121373450747856947683463616792101806445070648000277502684916746550586856935673420670581136429224554405758925724208241314695689016758940256776311356919292033376587141660230105703089634572075440370847469940168269282808481184289314848524948644871927809676271275775397027668605952496716674183485704422507197965004714951050492214776567636938662976979522110718264549734772662425709429322582798502585509785265383207606726317164309505995087807523710333101197857547331541421808427543863591778117054309827482385045648019095610299291824318237525357709750539565187697510374970888692180205189339507238539205144634197265287286965110862571492198849978748873771345686209167058',

		    // Pi (1025 digits).
		    PI = '3.1415926535897932384626433832795028841971693993751058209749445923078164062862089986280348253421170679821480865132823066470938446095505822317253594081284811174502841027019385211055596446229489549303819644288109756659334461284756482337867831652712019091456485669234603486104543266482133936072602491412737245870066063155881748815209209628292540917153643678925903600113305305488204665213841469519415116094330572703657595919530921861173819326117931051185480744623799627495673518857527248912279381830119491298336733624406566430860213949463952247371907021798609437027705392171762931767523846748184676694051320005681271452635608277857713427577896091736371787214684409012249534301465495853710507922796892589235420199561121290219608640344181598136297747713099605187072113499999983729780499510597317328160963185950244594553469083026425223082533446850352619311881710100031378387528865875332083814206171776691473035982534904287554687311595628638823537875937519577818577805321712268066130019278766111959092164201989380952572010654858632789',


		    // The initial configuration properties of the Decimal constructor.
		    DEFAULTS = {

		      // These values must be integers within the stated ranges (inclusive).
		      // Most of these values can be changed at run-time using the `Decimal.config` method.

		      // The maximum number of significant digits of the result of a calculation or base conversion.
		      // E.g. `Decimal.config({ precision: 20 });`
		      precision: 20,                         // 1 to MAX_DIGITS

		      // The rounding mode used when rounding to `precision`.
		      //
		      // ROUND_UP         0 Away from zero.
		      // ROUND_DOWN       1 Towards zero.
		      // ROUND_CEIL       2 Towards +Infinity.
		      // ROUND_FLOOR      3 Towards -Infinity.
		      // ROUND_HALF_UP    4 Towards nearest neighbour. If equidistant, up.
		      // ROUND_HALF_DOWN  5 Towards nearest neighbour. If equidistant, down.
		      // ROUND_HALF_EVEN  6 Towards nearest neighbour. If equidistant, towards even neighbour.
		      // ROUND_HALF_CEIL  7 Towards nearest neighbour. If equidistant, towards +Infinity.
		      // ROUND_HALF_FLOOR 8 Towards nearest neighbour. If equidistant, towards -Infinity.
		      //
		      // E.g.
		      // `Decimal.rounding = 4;`
		      // `Decimal.rounding = Decimal.ROUND_HALF_UP;`
		      rounding: 4,                           // 0 to 8

		      // The modulo mode used when calculating the modulus: a mod n.
		      // The quotient (q = a / n) is calculated according to the corresponding rounding mode.
		      // The remainder (r) is calculated as: r = a - n * q.
		      //
		      // UP         0 The remainder is positive if the dividend is negative, else is negative.
		      // DOWN       1 The remainder has the same sign as the dividend (JavaScript %).
		      // FLOOR      3 The remainder has the same sign as the divisor (Python %).
		      // HALF_EVEN  6 The IEEE 754 remainder function.
		      // EUCLID     9 Euclidian division. q = sign(n) * floor(a / abs(n)). Always positive.
		      //
		      // Truncated division (1), floored division (3), the IEEE 754 remainder (6), and Euclidian
		      // division (9) are commonly used for the modulus operation. The other rounding modes can also
		      // be used, but they may not give useful results.
		      modulo: 1,                             // 0 to 9

		      // The exponent value at and beneath which `toString` returns exponential notation.
		      // JavaScript numbers: -7
		      toExpNeg: -7,                          // 0 to -EXP_LIMIT

		      // The exponent value at and above which `toString` returns exponential notation.
		      // JavaScript numbers: 21
		      toExpPos:  21,                         // 0 to EXP_LIMIT

		      // The minimum exponent value, beneath which underflow to zero occurs.
		      // JavaScript numbers: -324  (5e-324)
		      minE: -EXP_LIMIT,                      // -1 to -EXP_LIMIT

		      // The maximum exponent value, above which overflow to Infinity occurs.
		      // JavaScript numbers: 308  (1.7976931348623157e+308)
		      maxE: EXP_LIMIT,                       // 1 to EXP_LIMIT

		      // Whether to use cryptographically-secure random number generation, if available.
		      crypto: false                          // true/false
		    },


		  // ----------------------------------- END OF EDITABLE DEFAULTS ------------------------------- //


		    Decimal, inexact, noConflict, quadrant,
		    external = true,

		    decimalError = '[DecimalError] ',
		    invalidArgument = decimalError + 'Invalid argument: ',
		    precisionLimitExceeded = decimalError + 'Precision limit exceeded',
		    cryptoUnavailable = decimalError + 'crypto unavailable',
		    tag = '[object Decimal]',

		    mathfloor = Math.floor,
		    mathpow = Math.pow,

		    isBinary = /^0b([01]+(\.[01]*)?|\.[01]+)(p[+-]?\d+)?$/i,
		    isHex = /^0x([0-9a-f]+(\.[0-9a-f]*)?|\.[0-9a-f]+)(p[+-]?\d+)?$/i,
		    isOctal = /^0o([0-7]+(\.[0-7]*)?|\.[0-7]+)(p[+-]?\d+)?$/i,
		    isDecimal = /^(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?$/i,

		    BASE = 1e7,
		    LOG_BASE = 7,
		    MAX_SAFE_INTEGER = 9007199254740991,

		    LN10_PRECISION = LN10.length - 1,
		    PI_PRECISION = PI.length - 1,

		    // Decimal.prototype object
		    P = { toStringTag: tag };


		  // Decimal prototype methods


		  /*
		   *  absoluteValue             abs
		   *  ceil
		   *  clampedTo                 clamp
		   *  comparedTo                cmp
		   *  cosine                    cos
		   *  cubeRoot                  cbrt
		   *  decimalPlaces             dp
		   *  dividedBy                 div
		   *  dividedToIntegerBy        divToInt
		   *  equals                    eq
		   *  floor
		   *  greaterThan               gt
		   *  greaterThanOrEqualTo      gte
		   *  hyperbolicCosine          cosh
		   *  hyperbolicSine            sinh
		   *  hyperbolicTangent         tanh
		   *  inverseCosine             acos
		   *  inverseHyperbolicCosine   acosh
		   *  inverseHyperbolicSine     asinh
		   *  inverseHyperbolicTangent  atanh
		   *  inverseSine               asin
		   *  inverseTangent            atan
		   *  isFinite
		   *  isInteger                 isInt
		   *  isNaN
		   *  isNegative                isNeg
		   *  isPositive                isPos
		   *  isZero
		   *  lessThan                  lt
		   *  lessThanOrEqualTo         lte
		   *  logarithm                 log
		   *  [maximum]                 [max]
		   *  [minimum]                 [min]
		   *  minus                     sub
		   *  modulo                    mod
		   *  naturalExponential        exp
		   *  naturalLogarithm          ln
		   *  negated                   neg
		   *  plus                      add
		   *  precision                 sd
		   *  round
		   *  sine                      sin
		   *  squareRoot                sqrt
		   *  tangent                   tan
		   *  times                     mul
		   *  toBinary
		   *  toDecimalPlaces           toDP
		   *  toExponential
		   *  toFixed
		   *  toFraction
		   *  toHexadecimal             toHex
		   *  toNearest
		   *  toNumber
		   *  toOctal
		   *  toPower                   pow
		   *  toPrecision
		   *  toSignificantDigits       toSD
		   *  toString
		   *  truncated                 trunc
		   *  valueOf                   toJSON
		   */


		  /*
		   * Return a new Decimal whose value is the absolute value of this Decimal.
		   *
		   */
		  P.absoluteValue = P.abs = function () {
		    var x = new this.constructor(this);
		    if (x.s < 0) x.s = 1;
		    return finalise(x);
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal rounded to a whole number in the
		   * direction of positive Infinity.
		   *
		   */
		  P.ceil = function () {
		    return finalise(new this.constructor(this), this.e + 1, 2);
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal clamped to the range
		   * delineated by `min` and `max`.
		   *
		   * min {number|string|bigint|Decimal}
		   * max {number|string|bigint|Decimal}
		   *
		   */
		  P.clampedTo = P.clamp = function (min, max) {
		    var k,
		      x = this,
		      Ctor = x.constructor;
		    min = new Ctor(min);
		    max = new Ctor(max);
		    if (!min.s || !max.s) return new Ctor(NaN);
		    if (min.gt(max)) throw Error(invalidArgument + max);
		    k = x.cmp(min);
		    return k < 0 ? min : x.cmp(max) > 0 ? max : new Ctor(x);
		  };


		  /*
		   * Return
		   *   1    if the value of this Decimal is greater than the value of `y`,
		   *  -1    if the value of this Decimal is less than the value of `y`,
		   *   0    if they have the same value,
		   *   NaN  if the value of either Decimal is NaN.
		   *
		   */
		  P.comparedTo = P.cmp = function (y) {
		    var i, j, xdL, ydL,
		      x = this,
		      xd = x.d,
		      yd = (y = new x.constructor(y)).d,
		      xs = x.s,
		      ys = y.s;

		    // Either NaN or ±Infinity?
		    if (!xd || !yd) {
		      return !xs || !ys ? NaN : xs !== ys ? xs : xd === yd ? 0 : !xd ^ xs < 0 ? 1 : -1;
		    }

		    // Either zero?
		    if (!xd[0] || !yd[0]) return xd[0] ? xs : yd[0] ? -ys : 0;

		    // Signs differ?
		    if (xs !== ys) return xs;

		    // Compare exponents.
		    if (x.e !== y.e) return x.e > y.e ^ xs < 0 ? 1 : -1;

		    xdL = xd.length;
		    ydL = yd.length;

		    // Compare digit by digit.
		    for (i = 0, j = xdL < ydL ? xdL : ydL; i < j; ++i) {
		      if (xd[i] !== yd[i]) return xd[i] > yd[i] ^ xs < 0 ? 1 : -1;
		    }

		    // Compare lengths.
		    return xdL === ydL ? 0 : xdL > ydL ^ xs < 0 ? 1 : -1;
		  };


		  /*
		   * Return a new Decimal whose value is the cosine of the value in radians of this Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-1, 1]
		   *
		   * cos(0)         = 1
		   * cos(-0)        = 1
		   * cos(Infinity)  = NaN
		   * cos(-Infinity) = NaN
		   * cos(NaN)       = NaN
		   *
		   */
		  P.cosine = P.cos = function () {
		    var pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.d) return new Ctor(NaN);

		    // cos(0) = cos(-0) = 1
		    if (!x.d[0]) return new Ctor(1);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + Math.max(x.e, x.sd()) + LOG_BASE;
		    Ctor.rounding = 1;

		    x = cosine(Ctor, toLessThanHalfPi(Ctor, x));

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return finalise(quadrant == 2 || quadrant == 3 ? x.neg() : x, pr, rm, true);
		  };


		  /*
		   *
		   * Return a new Decimal whose value is the cube root of the value of this Decimal, rounded to
		   * `precision` significant digits using rounding mode `rounding`.
		   *
		   *  cbrt(0)  =  0
		   *  cbrt(-0) = -0
		   *  cbrt(1)  =  1
		   *  cbrt(-1) = -1
		   *  cbrt(N)  =  N
		   *  cbrt(-I) = -I
		   *  cbrt(I)  =  I
		   *
		   * Math.cbrt(x) = (x < 0 ? -Math.pow(-x, 1/3) : Math.pow(x, 1/3))
		   *
		   */
		  P.cubeRoot = P.cbrt = function () {
		    var e, m, n, r, rep, s, sd, t, t3, t3plusx,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite() || x.isZero()) return new Ctor(x);
		    external = false;

		    // Initial estimate.
		    s = x.s * mathpow(x.s * x, 1 / 3);

		     // Math.cbrt underflow/overflow?
		     // Pass x to Math.pow as integer, then adjust the exponent of the result.
		    if (!s || Math.abs(s) == 1 / 0) {
		      n = digitsToString(x.d);
		      e = x.e;

		      // Adjust n exponent so it is a multiple of 3 away from x exponent.
		      if (s = (e - n.length + 1) % 3) n += (s == 1 || s == -2 ? '0' : '00');
		      s = mathpow(n, 1 / 3);

		      // Rarely, e may be one less than the result exponent value.
		      e = mathfloor((e + 1) / 3) - (e % 3 == (e < 0 ? -1 : 2));

		      if (s == 1 / 0) {
		        n = '5e' + e;
		      } else {
		        n = s.toExponential();
		        n = n.slice(0, n.indexOf('e') + 1) + e;
		      }

		      r = new Ctor(n);
		      r.s = x.s;
		    } else {
		      r = new Ctor(s.toString());
		    }

		    sd = (e = Ctor.precision) + 3;

		    // Halley's method.
		    // TODO? Compare Newton's method.
		    for (;;) {
		      t = r;
		      t3 = t.times(t).times(t);
		      t3plusx = t3.plus(x);
		      r = divide(t3plusx.plus(x).times(t), t3plusx.plus(t3), sd + 2, 1);

		      // TODO? Replace with for-loop and checkRoundingDigits.
		      if (digitsToString(t.d).slice(0, sd) === (n = digitsToString(r.d)).slice(0, sd)) {
		        n = n.slice(sd - 3, sd + 1);

		        // The 4th rounding digit may be in error by -1 so if the 4 rounding digits are 9999 or 4999
		        // , i.e. approaching a rounding boundary, continue the iteration.
		        if (n == '9999' || !rep && n == '4999') {

		          // On the first iteration only, check to see if rounding up gives the exact result as the
		          // nines may infinitely repeat.
		          if (!rep) {
		            finalise(t, e + 1, 0);

		            if (t.times(t).times(t).eq(x)) {
		              r = t;
		              break;
		            }
		          }

		          sd += 4;
		          rep = 1;
		        } else {

		          // If the rounding digits are null, 0{0,4} or 50{0,3}, check for an exact result.
		          // If not, then there are further digits and m will be truthy.
		          if (!+n || !+n.slice(1) && n.charAt(0) == '5') {

		            // Truncate to the first rounding digit.
		            finalise(r, e + 1, 1);
		            m = !r.times(r).times(r).eq(x);
		          }

		          break;
		        }
		      }
		    }

		    external = true;

		    return finalise(r, e, Ctor.rounding, m);
		  };


		  /*
		   * Return the number of decimal places of the value of this Decimal.
		   *
		   */
		  P.decimalPlaces = P.dp = function () {
		    var w,
		      d = this.d,
		      n = NaN;

		    if (d) {
		      w = d.length - 1;
		      n = (w - mathfloor(this.e / LOG_BASE)) * LOG_BASE;

		      // Subtract the number of trailing zeros of the last word.
		      w = d[w];
		      if (w) for (; w % 10 == 0; w /= 10) n--;
		      if (n < 0) n = 0;
		    }

		    return n;
		  };


		  /*
		   *  n / 0 = I
		   *  n / N = N
		   *  n / I = 0
		   *  0 / n = 0
		   *  0 / 0 = N
		   *  0 / N = N
		   *  0 / I = 0
		   *  N / n = N
		   *  N / 0 = N
		   *  N / N = N
		   *  N / I = N
		   *  I / n = I
		   *  I / 0 = I
		   *  I / N = N
		   *  I / I = N
		   *
		   * Return a new Decimal whose value is the value of this Decimal divided by `y`, rounded to
		   * `precision` significant digits using rounding mode `rounding`.
		   *
		   */
		  P.dividedBy = P.div = function (y) {
		    return divide(this, new this.constructor(y));
		  };


		  /*
		   * Return a new Decimal whose value is the integer part of dividing the value of this Decimal
		   * by the value of `y`, rounded to `precision` significant digits using rounding mode `rounding`.
		   *
		   */
		  P.dividedToIntegerBy = P.divToInt = function (y) {
		    var x = this,
		      Ctor = x.constructor;
		    return finalise(divide(x, new Ctor(y), 0, 1, 1), Ctor.precision, Ctor.rounding);
		  };


		  /*
		   * Return true if the value of this Decimal is equal to the value of `y`, otherwise return false.
		   *
		   */
		  P.equals = P.eq = function (y) {
		    return this.cmp(y) === 0;
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal rounded to a whole number in the
		   * direction of negative Infinity.
		   *
		   */
		  P.floor = function () {
		    return finalise(new this.constructor(this), this.e + 1, 3);
		  };


		  /*
		   * Return true if the value of this Decimal is greater than the value of `y`, otherwise return
		   * false.
		   *
		   */
		  P.greaterThan = P.gt = function (y) {
		    return this.cmp(y) > 0;
		  };


		  /*
		   * Return true if the value of this Decimal is greater than or equal to the value of `y`,
		   * otherwise return false.
		   *
		   */
		  P.greaterThanOrEqualTo = P.gte = function (y) {
		    var k = this.cmp(y);
		    return k == 1 || k === 0;
		  };


		  /*
		   * Return a new Decimal whose value is the hyperbolic cosine of the value in radians of this
		   * Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [1, Infinity]
		   *
		   * cosh(x) = 1 + x^2/2! + x^4/4! + x^6/6! + ...
		   *
		   * cosh(0)         = 1
		   * cosh(-0)        = 1
		   * cosh(Infinity)  = Infinity
		   * cosh(-Infinity) = Infinity
		   * cosh(NaN)       = NaN
		   *
		   *  x        time taken (ms)   result
		   * 1000      9                 9.8503555700852349694e+433
		   * 10000     25                4.4034091128314607936e+4342
		   * 100000    171               1.4033316802130615897e+43429
		   * 1000000   3817              1.5166076984010437725e+434294
		   * 10000000  abandoned after 2 minute wait
		   *
		   * TODO? Compare performance of cosh(x) = 0.5 * (exp(x) + exp(-x))
		   *
		   */
		  P.hyperbolicCosine = P.cosh = function () {
		    var k, n, pr, rm, len,
		      x = this,
		      Ctor = x.constructor,
		      one = new Ctor(1);

		    if (!x.isFinite()) return new Ctor(x.s ? 1 / 0 : NaN);
		    if (x.isZero()) return one;

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + Math.max(x.e, x.sd()) + 4;
		    Ctor.rounding = 1;
		    len = x.d.length;

		    // Argument reduction: cos(4x) = 1 - 8cos^2(x) + 8cos^4(x) + 1
		    // i.e. cos(x) = 1 - cos^2(x/4)(8 - 8cos^2(x/4))

		    // Estimate the optimum number of times to use the argument reduction.
		    // TODO? Estimation reused from cosine() and may not be optimal here.
		    if (len < 32) {
		      k = Math.ceil(len / 3);
		      n = (1 / tinyPow(4, k)).toString();
		    } else {
		      k = 16;
		      n = '2.3283064365386962890625e-10';
		    }

		    x = taylorSeries(Ctor, 1, x.times(n), new Ctor(1), true);

		    // Reverse argument reduction
		    var cosh2_x,
		      i = k,
		      d8 = new Ctor(8);
		    for (; i--;) {
		      cosh2_x = x.times(x);
		      x = one.minus(cosh2_x.times(d8.minus(cosh2_x.times(d8))));
		    }

		    return finalise(x, Ctor.precision = pr, Ctor.rounding = rm, true);
		  };


		  /*
		   * Return a new Decimal whose value is the hyperbolic sine of the value in radians of this
		   * Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-Infinity, Infinity]
		   *
		   * sinh(x) = x + x^3/3! + x^5/5! + x^7/7! + ...
		   *
		   * sinh(0)         = 0
		   * sinh(-0)        = -0
		   * sinh(Infinity)  = Infinity
		   * sinh(-Infinity) = -Infinity
		   * sinh(NaN)       = NaN
		   *
		   * x        time taken (ms)
		   * 10       2 ms
		   * 100      5 ms
		   * 1000     14 ms
		   * 10000    82 ms
		   * 100000   886 ms            1.4033316802130615897e+43429
		   * 200000   2613 ms
		   * 300000   5407 ms
		   * 400000   8824 ms
		   * 500000   13026 ms          8.7080643612718084129e+217146
		   * 1000000  48543 ms
		   *
		   * TODO? Compare performance of sinh(x) = 0.5 * (exp(x) - exp(-x))
		   *
		   */
		  P.hyperbolicSine = P.sinh = function () {
		    var k, pr, rm, len,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite() || x.isZero()) return new Ctor(x);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + Math.max(x.e, x.sd()) + 4;
		    Ctor.rounding = 1;
		    len = x.d.length;

		    if (len < 3) {
		      x = taylorSeries(Ctor, 2, x, x, true);
		    } else {

		      // Alternative argument reduction: sinh(3x) = sinh(x)(3 + 4sinh^2(x))
		      // i.e. sinh(x) = sinh(x/3)(3 + 4sinh^2(x/3))
		      // 3 multiplications and 1 addition

		      // Argument reduction: sinh(5x) = sinh(x)(5 + sinh^2(x)(20 + 16sinh^2(x)))
		      // i.e. sinh(x) = sinh(x/5)(5 + sinh^2(x/5)(20 + 16sinh^2(x/5)))
		      // 4 multiplications and 2 additions

		      // Estimate the optimum number of times to use the argument reduction.
		      k = 1.4 * Math.sqrt(len);
		      k = k > 16 ? 16 : k | 0;

		      x = x.times(1 / tinyPow(5, k));
		      x = taylorSeries(Ctor, 2, x, x, true);

		      // Reverse argument reduction
		      var sinh2_x,
		        d5 = new Ctor(5),
		        d16 = new Ctor(16),
		        d20 = new Ctor(20);
		      for (; k--;) {
		        sinh2_x = x.times(x);
		        x = x.times(d5.plus(sinh2_x.times(d16.times(sinh2_x).plus(d20))));
		      }
		    }

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return finalise(x, pr, rm, true);
		  };


		  /*
		   * Return a new Decimal whose value is the hyperbolic tangent of the value in radians of this
		   * Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-1, 1]
		   *
		   * tanh(x) = sinh(x) / cosh(x)
		   *
		   * tanh(0)         = 0
		   * tanh(-0)        = -0
		   * tanh(Infinity)  = 1
		   * tanh(-Infinity) = -1
		   * tanh(NaN)       = NaN
		   *
		   */
		  P.hyperbolicTangent = P.tanh = function () {
		    var pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite()) return new Ctor(x.s);
		    if (x.isZero()) return new Ctor(x);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + 7;
		    Ctor.rounding = 1;

		    return divide(x.sinh(), x.cosh(), Ctor.precision = pr, Ctor.rounding = rm);
		  };


		  /*
		   * Return a new Decimal whose value is the arccosine (inverse cosine) in radians of the value of
		   * this Decimal.
		   *
		   * Domain: [-1, 1]
		   * Range: [0, pi]
		   *
		   * acos(x) = pi/2 - asin(x)
		   *
		   * acos(0)       = pi/2
		   * acos(-0)      = pi/2
		   * acos(1)       = 0
		   * acos(-1)      = pi
		   * acos(1/2)     = pi/3
		   * acos(-1/2)    = 2*pi/3
		   * acos(|x| > 1) = NaN
		   * acos(NaN)     = NaN
		   *
		   */
		  P.inverseCosine = P.acos = function () {
		    var x = this,
		      Ctor = x.constructor,
		      k = x.abs().cmp(1),
		      pr = Ctor.precision,
		      rm = Ctor.rounding;

		    if (k !== -1) {
		      return k === 0
		        // |x| is 1
		        ? x.isNeg() ? getPi(Ctor, pr, rm) : new Ctor(0)
		        // |x| > 1 or x is NaN
		        : new Ctor(NaN);
		    }

		    if (x.isZero()) return getPi(Ctor, pr + 4, rm).times(0.5);

		    // TODO? Special case acos(0.5) = pi/3 and acos(-0.5) = 2*pi/3

		    Ctor.precision = pr + 6;
		    Ctor.rounding = 1;

		    // See https://github.com/MikeMcl/decimal.js/pull/217
		    x = new Ctor(1).minus(x).div(x.plus(1)).sqrt().atan();

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return x.times(2);
		  };


		  /*
		   * Return a new Decimal whose value is the inverse of the hyperbolic cosine in radians of the
		   * value of this Decimal.
		   *
		   * Domain: [1, Infinity]
		   * Range: [0, Infinity]
		   *
		   * acosh(x) = ln(x + sqrt(x^2 - 1))
		   *
		   * acosh(x < 1)     = NaN
		   * acosh(NaN)       = NaN
		   * acosh(Infinity)  = Infinity
		   * acosh(-Infinity) = NaN
		   * acosh(0)         = NaN
		   * acosh(-0)        = NaN
		   * acosh(1)         = 0
		   * acosh(-1)        = NaN
		   *
		   */
		  P.inverseHyperbolicCosine = P.acosh = function () {
		    var pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (x.lte(1)) return new Ctor(x.eq(1) ? 0 : NaN);
		    if (!x.isFinite()) return new Ctor(x);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + Math.max(Math.abs(x.e), x.sd()) + 4;
		    Ctor.rounding = 1;
		    external = false;

		    x = x.times(x).minus(1).sqrt().plus(x);

		    external = true;
		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return x.ln();
		  };


		  /*
		   * Return a new Decimal whose value is the inverse of the hyperbolic sine in radians of the value
		   * of this Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-Infinity, Infinity]
		   *
		   * asinh(x) = ln(x + sqrt(x^2 + 1))
		   *
		   * asinh(NaN)       = NaN
		   * asinh(Infinity)  = Infinity
		   * asinh(-Infinity) = -Infinity
		   * asinh(0)         = 0
		   * asinh(-0)        = -0
		   *
		   */
		  P.inverseHyperbolicSine = P.asinh = function () {
		    var pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite() || x.isZero()) return new Ctor(x);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + 2 * Math.max(Math.abs(x.e), x.sd()) + 6;
		    Ctor.rounding = 1;
		    external = false;

		    x = x.times(x).plus(1).sqrt().plus(x);

		    external = true;
		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return x.ln();
		  };


		  /*
		   * Return a new Decimal whose value is the inverse of the hyperbolic tangent in radians of the
		   * value of this Decimal.
		   *
		   * Domain: [-1, 1]
		   * Range: [-Infinity, Infinity]
		   *
		   * atanh(x) = 0.5 * ln((1 + x) / (1 - x))
		   *
		   * atanh(|x| > 1)   = NaN
		   * atanh(NaN)       = NaN
		   * atanh(Infinity)  = NaN
		   * atanh(-Infinity) = NaN
		   * atanh(0)         = 0
		   * atanh(-0)        = -0
		   * atanh(1)         = Infinity
		   * atanh(-1)        = -Infinity
		   *
		   */
		  P.inverseHyperbolicTangent = P.atanh = function () {
		    var pr, rm, wpr, xsd,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite()) return new Ctor(NaN);
		    if (x.e >= 0) return new Ctor(x.abs().eq(1) ? x.s / 0 : x.isZero() ? x : NaN);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    xsd = x.sd();

		    if (Math.max(xsd, pr) < 2 * -x.e - 1) return finalise(new Ctor(x), pr, rm, true);

		    Ctor.precision = wpr = xsd - x.e;

		    x = divide(x.plus(1), new Ctor(1).minus(x), wpr + pr, 1);

		    Ctor.precision = pr + 4;
		    Ctor.rounding = 1;

		    x = x.ln();

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return x.times(0.5);
		  };


		  /*
		   * Return a new Decimal whose value is the arcsine (inverse sine) in radians of the value of this
		   * Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-pi/2, pi/2]
		   *
		   * asin(x) = 2*atan(x/(1 + sqrt(1 - x^2)))
		   *
		   * asin(0)       = 0
		   * asin(-0)      = -0
		   * asin(1/2)     = pi/6
		   * asin(-1/2)    = -pi/6
		   * asin(1)       = pi/2
		   * asin(-1)      = -pi/2
		   * asin(|x| > 1) = NaN
		   * asin(NaN)     = NaN
		   *
		   * TODO? Compare performance of Taylor series.
		   *
		   */
		  P.inverseSine = P.asin = function () {
		    var halfPi, k,
		      pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (x.isZero()) return new Ctor(x);

		    k = x.abs().cmp(1);
		    pr = Ctor.precision;
		    rm = Ctor.rounding;

		    if (k !== -1) {

		      // |x| is 1
		      if (k === 0) {
		        halfPi = getPi(Ctor, pr + 4, rm).times(0.5);
		        halfPi.s = x.s;
		        return halfPi;
		      }

		      // |x| > 1 or x is NaN
		      return new Ctor(NaN);
		    }

		    // TODO? Special case asin(1/2) = pi/6 and asin(-1/2) = -pi/6

		    Ctor.precision = pr + 6;
		    Ctor.rounding = 1;

		    x = x.div(new Ctor(1).minus(x.times(x)).sqrt().plus(1)).atan();

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return x.times(2);
		  };


		  /*
		   * Return a new Decimal whose value is the arctangent (inverse tangent) in radians of the value
		   * of this Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-pi/2, pi/2]
		   *
		   * atan(x) = x - x^3/3 + x^5/5 - x^7/7 + ...
		   *
		   * atan(0)         = 0
		   * atan(-0)        = -0
		   * atan(1)         = pi/4
		   * atan(-1)        = -pi/4
		   * atan(Infinity)  = pi/2
		   * atan(-Infinity) = -pi/2
		   * atan(NaN)       = NaN
		   *
		   */
		  P.inverseTangent = P.atan = function () {
		    var i, j, k, n, px, t, r, wpr, x2,
		      x = this,
		      Ctor = x.constructor,
		      pr = Ctor.precision,
		      rm = Ctor.rounding;

		    if (!x.isFinite()) {
		      if (!x.s) return new Ctor(NaN);
		      if (pr + 4 <= PI_PRECISION) {
		        r = getPi(Ctor, pr + 4, rm).times(0.5);
		        r.s = x.s;
		        return r;
		      }
		    } else if (x.isZero()) {
		      return new Ctor(x);
		    } else if (x.abs().eq(1) && pr + 4 <= PI_PRECISION) {
		      r = getPi(Ctor, pr + 4, rm).times(0.25);
		      r.s = x.s;
		      return r;
		    }

		    Ctor.precision = wpr = pr + 10;
		    Ctor.rounding = 1;

		    // TODO? if (x >= 1 && pr <= PI_PRECISION) atan(x) = halfPi * x.s - atan(1 / x);

		    // Argument reduction
		    // Ensure |x| < 0.42
		    // atan(x) = 2 * atan(x / (1 + sqrt(1 + x^2)))

		    k = Math.min(28, wpr / LOG_BASE + 2 | 0);

		    for (i = k; i; --i) x = x.div(x.times(x).plus(1).sqrt().plus(1));

		    external = false;

		    j = Math.ceil(wpr / LOG_BASE);
		    n = 1;
		    x2 = x.times(x);
		    r = new Ctor(x);
		    px = x;

		    // atan(x) = x - x^3/3 + x^5/5 - x^7/7 + ...
		    for (; i !== -1;) {
		      px = px.times(x2);
		      t = r.minus(px.div(n += 2));

		      px = px.times(x2);
		      r = t.plus(px.div(n += 2));

		      if (r.d[j] !== void 0) for (i = j; r.d[i] === t.d[i] && i--;);
		    }

		    if (k) r = r.times(2 << (k - 1));

		    external = true;

		    return finalise(r, Ctor.precision = pr, Ctor.rounding = rm, true);
		  };


		  /*
		   * Return true if the value of this Decimal is a finite number, otherwise return false.
		   *
		   */
		  P.isFinite = function () {
		    return !!this.d;
		  };


		  /*
		   * Return true if the value of this Decimal is an integer, otherwise return false.
		   *
		   */
		  P.isInteger = P.isInt = function () {
		    return !!this.d && mathfloor(this.e / LOG_BASE) > this.d.length - 2;
		  };


		  /*
		   * Return true if the value of this Decimal is NaN, otherwise return false.
		   *
		   */
		  P.isNaN = function () {
		    return !this.s;
		  };


		  /*
		   * Return true if the value of this Decimal is negative, otherwise return false.
		   *
		   */
		  P.isNegative = P.isNeg = function () {
		    return this.s < 0;
		  };


		  /*
		   * Return true if the value of this Decimal is positive, otherwise return false.
		   *
		   */
		  P.isPositive = P.isPos = function () {
		    return this.s > 0;
		  };


		  /*
		   * Return true if the value of this Decimal is 0 or -0, otherwise return false.
		   *
		   */
		  P.isZero = function () {
		    return !!this.d && this.d[0] === 0;
		  };


		  /*
		   * Return true if the value of this Decimal is less than `y`, otherwise return false.
		   *
		   */
		  P.lessThan = P.lt = function (y) {
		    return this.cmp(y) < 0;
		  };


		  /*
		   * Return true if the value of this Decimal is less than or equal to `y`, otherwise return false.
		   *
		   */
		  P.lessThanOrEqualTo = P.lte = function (y) {
		    return this.cmp(y) < 1;
		  };


		  /*
		   * Return the logarithm of the value of this Decimal to the specified base, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * If no base is specified, return log[10](arg).
		   *
		   * log[base](arg) = ln(arg) / ln(base)
		   *
		   * The result will always be correctly rounded if the base of the log is 10, and 'almost always'
		   * otherwise:
		   *
		   * Depending on the rounding mode, the result may be incorrectly rounded if the first fifteen
		   * rounding digits are [49]99999999999999 or [50]00000000000000. In that case, the maximum error
		   * between the result and the correctly rounded result will be one ulp (unit in the last place).
		   *
		   * log[-b](a)       = NaN
		   * log[0](a)        = NaN
		   * log[1](a)        = NaN
		   * log[NaN](a)      = NaN
		   * log[Infinity](a) = NaN
		   * log[b](0)        = -Infinity
		   * log[b](-0)       = -Infinity
		   * log[b](-a)       = NaN
		   * log[b](1)        = 0
		   * log[b](Infinity) = Infinity
		   * log[b](NaN)      = NaN
		   *
		   * [base] {number|string|bigint|Decimal} The base of the logarithm.
		   *
		   */
		  P.logarithm = P.log = function (base) {
		    var isBase10, d, denominator, k, inf, num, sd, r,
		      arg = this,
		      Ctor = arg.constructor,
		      pr = Ctor.precision,
		      rm = Ctor.rounding,
		      guard = 5;

		    // Default base is 10.
		    if (base == null) {
		      base = new Ctor(10);
		      isBase10 = true;
		    } else {
		      base = new Ctor(base);
		      d = base.d;

		      // Return NaN if base is negative, or non-finite, or is 0 or 1.
		      if (base.s < 0 || !d || !d[0] || base.eq(1)) return new Ctor(NaN);

		      isBase10 = base.eq(10);
		    }

		    d = arg.d;

		    // Is arg negative, non-finite, 0 or 1?
		    if (arg.s < 0 || !d || !d[0] || arg.eq(1)) {
		      return new Ctor(d && !d[0] ? -1 / 0 : arg.s != 1 ? NaN : d ? 0 : 1 / 0);
		    }

		    // The result will have a non-terminating decimal expansion if base is 10 and arg is not an
		    // integer power of 10.
		    if (isBase10) {
		      if (d.length > 1) {
		        inf = true;
		      } else {
		        for (k = d[0]; k % 10 === 0;) k /= 10;
		        inf = k !== 1;
		      }
		    }

		    external = false;
		    sd = pr + guard;
		    num = naturalLogarithm(arg, sd);
		    denominator = isBase10 ? getLn10(Ctor, sd + 10) : naturalLogarithm(base, sd);

		    // The result will have 5 rounding digits.
		    r = divide(num, denominator, sd, 1);

		    // If at a rounding boundary, i.e. the result's rounding digits are [49]9999 or [50]0000,
		    // calculate 10 further digits.
		    //
		    // If the result is known to have an infinite decimal expansion, repeat this until it is clear
		    // that the result is above or below the boundary. Otherwise, if after calculating the 10
		    // further digits, the last 14 are nines, round up and assume the result is exact.
		    // Also assume the result is exact if the last 14 are zero.
		    //
		    // Example of a result that will be incorrectly rounded:
		    // log[1048576](4503599627370502) = 2.60000000000000009610279511444746...
		    // The above result correctly rounded using ROUND_CEIL to 1 decimal place should be 2.7, but it
		    // will be given as 2.6 as there are 15 zeros immediately after the requested decimal place, so
		    // the exact result would be assumed to be 2.6, which rounded using ROUND_CEIL to 1 decimal
		    // place is still 2.6.
		    if (checkRoundingDigits(r.d, k = pr, rm)) {

		      do {
		        sd += 10;
		        num = naturalLogarithm(arg, sd);
		        denominator = isBase10 ? getLn10(Ctor, sd + 10) : naturalLogarithm(base, sd);
		        r = divide(num, denominator, sd, 1);

		        if (!inf) {

		          // Check for 14 nines from the 2nd rounding digit, as the first may be 4.
		          if (+digitsToString(r.d).slice(k + 1, k + 15) + 1 == 1e14) {
		            r = finalise(r, pr + 1, 0);
		          }

		          break;
		        }
		      } while (checkRoundingDigits(r.d, k += 10, rm));
		    }

		    external = true;

		    return finalise(r, pr, rm);
		  };


		  /*
		   * Return a new Decimal whose value is the maximum of the arguments and the value of this Decimal.
		   *
		   * arguments {number|string|bigint|Decimal}
		   *
		  P.max = function () {
		    Array.prototype.push.call(arguments, this);
		    return maxOrMin(this.constructor, arguments, -1);
		  };
		   */


		  /*
		   * Return a new Decimal whose value is the minimum of the arguments and the value of this Decimal.
		   *
		   * arguments {number|string|bigint|Decimal}
		   *
		  P.min = function () {
		    Array.prototype.push.call(arguments, this);
		    return maxOrMin(this.constructor, arguments, 1);
		  };
		   */


		  /*
		   *  n - 0 = n
		   *  n - N = N
		   *  n - I = -I
		   *  0 - n = -n
		   *  0 - 0 = 0
		   *  0 - N = N
		   *  0 - I = -I
		   *  N - n = N
		   *  N - 0 = N
		   *  N - N = N
		   *  N - I = N
		   *  I - n = I
		   *  I - 0 = I
		   *  I - N = N
		   *  I - I = N
		   *
		   * Return a new Decimal whose value is the value of this Decimal minus `y`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   */
		  P.minus = P.sub = function (y) {
		    var d, e, i, j, k, len, pr, rm, xd, xe, xLTy, yd,
		      x = this,
		      Ctor = x.constructor;

		    y = new Ctor(y);

		    // If either is not finite...
		    if (!x.d || !y.d) {

		      // Return NaN if either is NaN.
		      if (!x.s || !y.s) y = new Ctor(NaN);

		      // Return y negated if x is finite and y is ±Infinity.
		      else if (x.d) y.s = -y.s;

		      // Return x if y is finite and x is ±Infinity.
		      // Return x if both are ±Infinity with different signs.
		      // Return NaN if both are ±Infinity with the same sign.
		      else y = new Ctor(y.d || x.s !== y.s ? x : NaN);

		      return y;
		    }

		    // If signs differ...
		    if (x.s != y.s) {
		      y.s = -y.s;
		      return x.plus(y);
		    }

		    xd = x.d;
		    yd = y.d;
		    pr = Ctor.precision;
		    rm = Ctor.rounding;

		    // If either is zero...
		    if (!xd[0] || !yd[0]) {

		      // Return y negated if x is zero and y is non-zero.
		      if (yd[0]) y.s = -y.s;

		      // Return x if y is zero and x is non-zero.
		      else if (xd[0]) y = new Ctor(x);

		      // Return zero if both are zero.
		      // From IEEE 754 (2008) 6.3: 0 - 0 = -0 - -0 = -0 when rounding to -Infinity.
		      else return new Ctor(rm === 3 ? -0 : 0);

		      return external ? finalise(y, pr, rm) : y;
		    }

		    // x and y are finite, non-zero numbers with the same sign.

		    // Calculate base 1e7 exponents.
		    e = mathfloor(y.e / LOG_BASE);
		    xe = mathfloor(x.e / LOG_BASE);

		    xd = xd.slice();
		    k = xe - e;

		    // If base 1e7 exponents differ...
		    if (k) {
		      xLTy = k < 0;

		      if (xLTy) {
		        d = xd;
		        k = -k;
		        len = yd.length;
		      } else {
		        d = yd;
		        e = xe;
		        len = xd.length;
		      }

		      // Numbers with massively different exponents would result in a very high number of
		      // zeros needing to be prepended, but this can be avoided while still ensuring correct
		      // rounding by limiting the number of zeros to `Math.ceil(pr / LOG_BASE) + 2`.
		      i = Math.max(Math.ceil(pr / LOG_BASE), len) + 2;

		      if (k > i) {
		        k = i;
		        d.length = 1;
		      }

		      // Prepend zeros to equalise exponents.
		      d.reverse();
		      for (i = k; i--;) d.push(0);
		      d.reverse();

		    // Base 1e7 exponents equal.
		    } else {

		      // Check digits to determine which is the bigger number.

		      i = xd.length;
		      len = yd.length;
		      xLTy = i < len;
		      if (xLTy) len = i;

		      for (i = 0; i < len; i++) {
		        if (xd[i] != yd[i]) {
		          xLTy = xd[i] < yd[i];
		          break;
		        }
		      }

		      k = 0;
		    }

		    if (xLTy) {
		      d = xd;
		      xd = yd;
		      yd = d;
		      y.s = -y.s;
		    }

		    len = xd.length;

		    // Append zeros to `xd` if shorter.
		    // Don't add zeros to `yd` if shorter as subtraction only needs to start at `yd` length.
		    for (i = yd.length - len; i > 0; --i) xd[len++] = 0;

		    // Subtract yd from xd.
		    for (i = yd.length; i > k;) {

		      if (xd[--i] < yd[i]) {
		        for (j = i; j && xd[--j] === 0;) xd[j] = BASE - 1;
		        --xd[j];
		        xd[i] += BASE;
		      }

		      xd[i] -= yd[i];
		    }

		    // Remove trailing zeros.
		    for (; xd[--len] === 0;) xd.pop();

		    // Remove leading zeros and adjust exponent accordingly.
		    for (; xd[0] === 0; xd.shift()) --e;

		    // Zero?
		    if (!xd[0]) return new Ctor(rm === 3 ? -0 : 0);

		    y.d = xd;
		    y.e = getBase10Exponent(xd, e);

		    return external ? finalise(y, pr, rm) : y;
		  };


		  /*
		   *   n % 0 =  N
		   *   n % N =  N
		   *   n % I =  n
		   *   0 % n =  0
		   *  -0 % n = -0
		   *   0 % 0 =  N
		   *   0 % N =  N
		   *   0 % I =  0
		   *   N % n =  N
		   *   N % 0 =  N
		   *   N % N =  N
		   *   N % I =  N
		   *   I % n =  N
		   *   I % 0 =  N
		   *   I % N =  N
		   *   I % I =  N
		   *
		   * Return a new Decimal whose value is the value of this Decimal modulo `y`, rounded to
		   * `precision` significant digits using rounding mode `rounding`.
		   *
		   * The result depends on the modulo mode.
		   *
		   */
		  P.modulo = P.mod = function (y) {
		    var q,
		      x = this,
		      Ctor = x.constructor;

		    y = new Ctor(y);

		    // Return NaN if x is ±Infinity or NaN, or y is NaN or ±0.
		    if (!x.d || !y.s || y.d && !y.d[0]) return new Ctor(NaN);

		    // Return x if y is ±Infinity or x is ±0.
		    if (!y.d || x.d && !x.d[0]) {
		      return finalise(new Ctor(x), Ctor.precision, Ctor.rounding);
		    }

		    // Prevent rounding of intermediate calculations.
		    external = false;

		    if (Ctor.modulo == 9) {

		      // Euclidian division: q = sign(y) * floor(x / abs(y))
		      // result = x - q * y    where  0 <= result < abs(y)
		      q = divide(x, y.abs(), 0, 3, 1);
		      q.s *= y.s;
		    } else {
		      q = divide(x, y, 0, Ctor.modulo, 1);
		    }

		    q = q.times(y);

		    external = true;

		    return x.minus(q);
		  };


		  /*
		   * Return a new Decimal whose value is the natural exponential of the value of this Decimal,
		   * i.e. the base e raised to the power the value of this Decimal, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   */
		  P.naturalExponential = P.exp = function () {
		    return naturalExponential(this);
		  };


		  /*
		   * Return a new Decimal whose value is the natural logarithm of the value of this Decimal,
		   * rounded to `precision` significant digits using rounding mode `rounding`.
		   *
		   */
		  P.naturalLogarithm = P.ln = function () {
		    return naturalLogarithm(this);
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal negated, i.e. as if multiplied by
		   * -1.
		   *
		   */
		  P.negated = P.neg = function () {
		    var x = new this.constructor(this);
		    x.s = -x.s;
		    return finalise(x);
		  };


		  /*
		   *  n + 0 = n
		   *  n + N = N
		   *  n + I = I
		   *  0 + n = n
		   *  0 + 0 = 0
		   *  0 + N = N
		   *  0 + I = I
		   *  N + n = N
		   *  N + 0 = N
		   *  N + N = N
		   *  N + I = N
		   *  I + n = I
		   *  I + 0 = I
		   *  I + N = N
		   *  I + I = I
		   *
		   * Return a new Decimal whose value is the value of this Decimal plus `y`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   */
		  P.plus = P.add = function (y) {
		    var carry, d, e, i, k, len, pr, rm, xd, yd,
		      x = this,
		      Ctor = x.constructor;

		    y = new Ctor(y);

		    // If either is not finite...
		    if (!x.d || !y.d) {

		      // Return NaN if either is NaN.
		      if (!x.s || !y.s) y = new Ctor(NaN);

		      // Return x if y is finite and x is ±Infinity.
		      // Return x if both are ±Infinity with the same sign.
		      // Return NaN if both are ±Infinity with different signs.
		      // Return y if x is finite and y is ±Infinity.
		      else if (!x.d) y = new Ctor(y.d || x.s === y.s ? x : NaN);

		      return y;
		    }

		     // If signs differ...
		    if (x.s != y.s) {
		      y.s = -y.s;
		      return x.minus(y);
		    }

		    xd = x.d;
		    yd = y.d;
		    pr = Ctor.precision;
		    rm = Ctor.rounding;

		    // If either is zero...
		    if (!xd[0] || !yd[0]) {

		      // Return x if y is zero.
		      // Return y if y is non-zero.
		      if (!yd[0]) y = new Ctor(x);

		      return external ? finalise(y, pr, rm) : y;
		    }

		    // x and y are finite, non-zero numbers with the same sign.

		    // Calculate base 1e7 exponents.
		    k = mathfloor(x.e / LOG_BASE);
		    e = mathfloor(y.e / LOG_BASE);

		    xd = xd.slice();
		    i = k - e;

		    // If base 1e7 exponents differ...
		    if (i) {

		      if (i < 0) {
		        d = xd;
		        i = -i;
		        len = yd.length;
		      } else {
		        d = yd;
		        e = k;
		        len = xd.length;
		      }

		      // Limit number of zeros prepended to max(ceil(pr / LOG_BASE), len) + 1.
		      k = Math.ceil(pr / LOG_BASE);
		      len = k > len ? k + 1 : len + 1;

		      if (i > len) {
		        i = len;
		        d.length = 1;
		      }

		      // Prepend zeros to equalise exponents. Note: Faster to use reverse then do unshifts.
		      d.reverse();
		      for (; i--;) d.push(0);
		      d.reverse();
		    }

		    len = xd.length;
		    i = yd.length;

		    // If yd is longer than xd, swap xd and yd so xd points to the longer array.
		    if (len - i < 0) {
		      i = len;
		      d = yd;
		      yd = xd;
		      xd = d;
		    }

		    // Only start adding at yd.length - 1 as the further digits of xd can be left as they are.
		    for (carry = 0; i;) {
		      carry = (xd[--i] = xd[i] + yd[i] + carry) / BASE | 0;
		      xd[i] %= BASE;
		    }

		    if (carry) {
		      xd.unshift(carry);
		      ++e;
		    }

		    // Remove trailing zeros.
		    // No need to check for zero, as +x + +y != 0 && -x + -y != 0
		    for (len = xd.length; xd[--len] == 0;) xd.pop();

		    y.d = xd;
		    y.e = getBase10Exponent(xd, e);

		    return external ? finalise(y, pr, rm) : y;
		  };


		  /*
		   * Return the number of significant digits of the value of this Decimal.
		   *
		   * [z] {boolean|number} Whether to count integer-part trailing zeros: true, false, 1 or 0.
		   *
		   */
		  P.precision = P.sd = function (z) {
		    var k,
		      x = this;

		    if (z !== void 0 && z !== !!z && z !== 1 && z !== 0) throw Error(invalidArgument + z);

		    if (x.d) {
		      k = getPrecision(x.d);
		      if (z && x.e + 1 > k) k = x.e + 1;
		    } else {
		      k = NaN;
		    }

		    return k;
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal rounded to a whole number using
		   * rounding mode `rounding`.
		   *
		   */
		  P.round = function () {
		    var x = this,
		      Ctor = x.constructor;

		    return finalise(new Ctor(x), x.e + 1, Ctor.rounding);
		  };


		  /*
		   * Return a new Decimal whose value is the sine of the value in radians of this Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-1, 1]
		   *
		   * sin(x) = x - x^3/3! + x^5/5! - ...
		   *
		   * sin(0)         = 0
		   * sin(-0)        = -0
		   * sin(Infinity)  = NaN
		   * sin(-Infinity) = NaN
		   * sin(NaN)       = NaN
		   *
		   */
		  P.sine = P.sin = function () {
		    var pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite()) return new Ctor(NaN);
		    if (x.isZero()) return new Ctor(x);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + Math.max(x.e, x.sd()) + LOG_BASE;
		    Ctor.rounding = 1;

		    x = sine(Ctor, toLessThanHalfPi(Ctor, x));

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return finalise(quadrant > 2 ? x.neg() : x, pr, rm, true);
		  };


		  /*
		   * Return a new Decimal whose value is the square root of this Decimal, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   *  sqrt(-n) =  N
		   *  sqrt(N)  =  N
		   *  sqrt(-I) =  N
		   *  sqrt(I)  =  I
		   *  sqrt(0)  =  0
		   *  sqrt(-0) = -0
		   *
		   */
		  P.squareRoot = P.sqrt = function () {
		    var m, n, sd, r, rep, t,
		      x = this,
		      d = x.d,
		      e = x.e,
		      s = x.s,
		      Ctor = x.constructor;

		    // Negative/NaN/Infinity/zero?
		    if (s !== 1 || !d || !d[0]) {
		      return new Ctor(!s || s < 0 && (!d || d[0]) ? NaN : d ? x : 1 / 0);
		    }

		    external = false;

		    // Initial estimate.
		    s = Math.sqrt(+x);

		    // Math.sqrt underflow/overflow?
		    // Pass x to Math.sqrt as integer, then adjust the exponent of the result.
		    if (s == 0 || s == 1 / 0) {
		      n = digitsToString(d);

		      if ((n.length + e) % 2 == 0) n += '0';
		      s = Math.sqrt(n);
		      e = mathfloor((e + 1) / 2) - (e < 0 || e % 2);

		      if (s == 1 / 0) {
		        n = '5e' + e;
		      } else {
		        n = s.toExponential();
		        n = n.slice(0, n.indexOf('e') + 1) + e;
		      }

		      r = new Ctor(n);
		    } else {
		      r = new Ctor(s.toString());
		    }

		    sd = (e = Ctor.precision) + 3;

		    // Newton-Raphson iteration.
		    for (;;) {
		      t = r;
		      r = t.plus(divide(x, t, sd + 2, 1)).times(0.5);

		      // TODO? Replace with for-loop and checkRoundingDigits.
		      if (digitsToString(t.d).slice(0, sd) === (n = digitsToString(r.d)).slice(0, sd)) {
		        n = n.slice(sd - 3, sd + 1);

		        // The 4th rounding digit may be in error by -1 so if the 4 rounding digits are 9999 or
		        // 4999, i.e. approaching a rounding boundary, continue the iteration.
		        if (n == '9999' || !rep && n == '4999') {

		          // On the first iteration only, check to see if rounding up gives the exact result as the
		          // nines may infinitely repeat.
		          if (!rep) {
		            finalise(t, e + 1, 0);

		            if (t.times(t).eq(x)) {
		              r = t;
		              break;
		            }
		          }

		          sd += 4;
		          rep = 1;
		        } else {

		          // If the rounding digits are null, 0{0,4} or 50{0,3}, check for an exact result.
		          // If not, then there are further digits and m will be truthy.
		          if (!+n || !+n.slice(1) && n.charAt(0) == '5') {

		            // Truncate to the first rounding digit.
		            finalise(r, e + 1, 1);
		            m = !r.times(r).eq(x);
		          }

		          break;
		        }
		      }
		    }

		    external = true;

		    return finalise(r, e, Ctor.rounding, m);
		  };


		  /*
		   * Return a new Decimal whose value is the tangent of the value in radians of this Decimal.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-Infinity, Infinity]
		   *
		   * tan(0)         = 0
		   * tan(-0)        = -0
		   * tan(Infinity)  = NaN
		   * tan(-Infinity) = NaN
		   * tan(NaN)       = NaN
		   *
		   */
		  P.tangent = P.tan = function () {
		    var pr, rm,
		      x = this,
		      Ctor = x.constructor;

		    if (!x.isFinite()) return new Ctor(NaN);
		    if (x.isZero()) return new Ctor(x);

		    pr = Ctor.precision;
		    rm = Ctor.rounding;
		    Ctor.precision = pr + 10;
		    Ctor.rounding = 1;

		    x = x.sin();
		    x.s = 1;
		    x = divide(x, new Ctor(1).minus(x.times(x)).sqrt(), pr + 10, 0);

		    Ctor.precision = pr;
		    Ctor.rounding = rm;

		    return finalise(quadrant == 2 || quadrant == 4 ? x.neg() : x, pr, rm, true);
		  };


		  /*
		   *  n * 0 = 0
		   *  n * N = N
		   *  n * I = I
		   *  0 * n = 0
		   *  0 * 0 = 0
		   *  0 * N = N
		   *  0 * I = N
		   *  N * n = N
		   *  N * 0 = N
		   *  N * N = N
		   *  N * I = N
		   *  I * n = I
		   *  I * 0 = N
		   *  I * N = N
		   *  I * I = I
		   *
		   * Return a new Decimal whose value is this Decimal times `y`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   */
		  P.times = P.mul = function (y) {
		    var carry, e, i, k, r, rL, t, xdL, ydL,
		      x = this,
		      Ctor = x.constructor,
		      xd = x.d,
		      yd = (y = new Ctor(y)).d;

		    y.s *= x.s;

		     // If either is NaN, ±Infinity or ±0...
		    if (!xd || !xd[0] || !yd || !yd[0]) {

		      return new Ctor(!y.s || xd && !xd[0] && !yd || yd && !yd[0] && !xd

		        // Return NaN if either is NaN.
		        // Return NaN if x is ±0 and y is ±Infinity, or y is ±0 and x is ±Infinity.
		        ? NaN

		        // Return ±Infinity if either is ±Infinity.
		        // Return ±0 if either is ±0.
		        : !xd || !yd ? y.s / 0 : y.s * 0);
		    }

		    e = mathfloor(x.e / LOG_BASE) + mathfloor(y.e / LOG_BASE);
		    xdL = xd.length;
		    ydL = yd.length;

		    // Ensure xd points to the longer array.
		    if (xdL < ydL) {
		      r = xd;
		      xd = yd;
		      yd = r;
		      rL = xdL;
		      xdL = ydL;
		      ydL = rL;
		    }

		    // Initialise the result array with zeros.
		    r = [];
		    rL = xdL + ydL;
		    for (i = rL; i--;) r.push(0);

		    // Multiply!
		    for (i = ydL; --i >= 0;) {
		      carry = 0;
		      for (k = xdL + i; k > i;) {
		        t = r[k] + yd[i] * xd[k - i - 1] + carry;
		        r[k--] = t % BASE | 0;
		        carry = t / BASE | 0;
		      }

		      r[k] = (r[k] + carry) % BASE | 0;
		    }

		    // Remove trailing zeros.
		    for (; !r[--rL];) r.pop();

		    if (carry) ++e;
		    else r.shift();

		    y.d = r;
		    y.e = getBase10Exponent(r, e);

		    return external ? finalise(y, Ctor.precision, Ctor.rounding) : y;
		  };


		  /*
		   * Return a string representing the value of this Decimal in base 2, round to `sd` significant
		   * digits using rounding mode `rm`.
		   *
		   * If the optional `sd` argument is present then return binary exponential notation.
		   *
		   * [sd] {number} Significant digits. Integer, 1 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   */
		  P.toBinary = function (sd, rm) {
		    return toStringBinary(this, 2, sd, rm);
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal rounded to a maximum of `dp`
		   * decimal places using rounding mode `rm` or `rounding` if `rm` is omitted.
		   *
		   * If `dp` is omitted, return a new Decimal whose value is the value of this Decimal.
		   *
		   * [dp] {number} Decimal places. Integer, 0 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   */
		  P.toDecimalPlaces = P.toDP = function (dp, rm) {
		    var x = this,
		      Ctor = x.constructor;

		    x = new Ctor(x);
		    if (dp === void 0) return x;

		    checkInt32(dp, 0, MAX_DIGITS);

		    if (rm === void 0) rm = Ctor.rounding;
		    else checkInt32(rm, 0, 8);

		    return finalise(x, dp + x.e + 1, rm);
		  };


		  /*
		   * Return a string representing the value of this Decimal in exponential notation rounded to
		   * `dp` fixed decimal places using rounding mode `rounding`.
		   *
		   * [dp] {number} Decimal places. Integer, 0 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   */
		  P.toExponential = function (dp, rm) {
		    var str,
		      x = this,
		      Ctor = x.constructor;

		    if (dp === void 0) {
		      str = finiteToString(x, true);
		    } else {
		      checkInt32(dp, 0, MAX_DIGITS);

		      if (rm === void 0) rm = Ctor.rounding;
		      else checkInt32(rm, 0, 8);

		      x = finalise(new Ctor(x), dp + 1, rm);
		      str = finiteToString(x, true, dp + 1);
		    }

		    return x.isNeg() && !x.isZero() ? '-' + str : str;
		  };


		  /*
		   * Return a string representing the value of this Decimal in normal (fixed-point) notation to
		   * `dp` fixed decimal places and rounded using rounding mode `rm` or `rounding` if `rm` is
		   * omitted.
		   *
		   * As with JavaScript numbers, (-0).toFixed(0) is '0', but e.g. (-0.00001).toFixed(0) is '-0'.
		   *
		   * [dp] {number} Decimal places. Integer, 0 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   * (-0).toFixed(0) is '0', but (-0.1).toFixed(0) is '-0'.
		   * (-0).toFixed(1) is '0.0', but (-0.01).toFixed(1) is '-0.0'.
		   * (-0).toFixed(3) is '0.000'.
		   * (-0.5).toFixed(0) is '-0'.
		   *
		   */
		  P.toFixed = function (dp, rm) {
		    var str, y,
		      x = this,
		      Ctor = x.constructor;

		    if (dp === void 0) {
		      str = finiteToString(x);
		    } else {
		      checkInt32(dp, 0, MAX_DIGITS);

		      if (rm === void 0) rm = Ctor.rounding;
		      else checkInt32(rm, 0, 8);

		      y = finalise(new Ctor(x), dp + x.e + 1, rm);
		      str = finiteToString(y, false, dp + y.e + 1);
		    }

		    // To determine whether to add the minus sign look at the value before it was rounded,
		    // i.e. look at `x` rather than `y`.
		    return x.isNeg() && !x.isZero() ? '-' + str : str;
		  };


		  /*
		   * Return an array representing the value of this Decimal as a simple fraction with an integer
		   * numerator and an integer denominator.
		   *
		   * The denominator will be a positive non-zero value less than or equal to the specified maximum
		   * denominator. If a maximum denominator is not specified, the denominator will be the lowest
		   * value necessary to represent the number exactly.
		   *
		   * [maxD] {number|string|bigint|Decimal} Maximum denominator. Integer >= 1 and < Infinity.
		   *
		   */
		  P.toFraction = function (maxD) {
		    var d, d0, d1, d2, e, k, n, n0, n1, pr, q, r,
		      x = this,
		      xd = x.d,
		      Ctor = x.constructor;

		    if (!xd) return new Ctor(x);

		    n1 = d0 = new Ctor(1);
		    d1 = n0 = new Ctor(0);

		    d = new Ctor(d1);
		    e = d.e = getPrecision(xd) - x.e - 1;
		    k = e % LOG_BASE;
		    d.d[0] = mathpow(10, k < 0 ? LOG_BASE + k : k);

		    if (maxD == null) {

		      // d is 10**e, the minimum max-denominator needed.
		      maxD = e > 0 ? d : n1;
		    } else {
		      n = new Ctor(maxD);
		      if (!n.isInt() || n.lt(n1)) throw Error(invalidArgument + n);
		      maxD = n.gt(d) ? (e > 0 ? d : n1) : n;
		    }

		    external = false;
		    n = new Ctor(digitsToString(xd));
		    pr = Ctor.precision;
		    Ctor.precision = e = xd.length * LOG_BASE * 2;

		    for (;;)  {
		      q = divide(n, d, 0, 1, 1);
		      d2 = d0.plus(q.times(d1));
		      if (d2.cmp(maxD) == 1) break;
		      d0 = d1;
		      d1 = d2;
		      d2 = n1;
		      n1 = n0.plus(q.times(d2));
		      n0 = d2;
		      d2 = d;
		      d = n.minus(q.times(d2));
		      n = d2;
		    }

		    d2 = divide(maxD.minus(d0), d1, 0, 1, 1);
		    n0 = n0.plus(d2.times(n1));
		    d0 = d0.plus(d2.times(d1));
		    n0.s = n1.s = x.s;

		    // Determine which fraction is closer to x, n0/d0 or n1/d1?
		    r = divide(n1, d1, e, 1).minus(x).abs().cmp(divide(n0, d0, e, 1).minus(x).abs()) < 1
		        ? [n1, d1] : [n0, d0];

		    Ctor.precision = pr;
		    external = true;

		    return r;
		  };


		  /*
		   * Return a string representing the value of this Decimal in base 16, round to `sd` significant
		   * digits using rounding mode `rm`.
		   *
		   * If the optional `sd` argument is present then return binary exponential notation.
		   *
		   * [sd] {number} Significant digits. Integer, 1 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   */
		  P.toHexadecimal = P.toHex = function (sd, rm) {
		    return toStringBinary(this, 16, sd, rm);
		  };


		  /*
		   * Returns a new Decimal whose value is the nearest multiple of `y` in the direction of rounding
		   * mode `rm`, or `Decimal.rounding` if `rm` is omitted, to the value of this Decimal.
		   *
		   * The return value will always have the same sign as this Decimal, unless either this Decimal
		   * or `y` is NaN, in which case the return value will be also be NaN.
		   *
		   * The return value is not affected by the value of `precision`.
		   *
		   * y {number|string|bigint|Decimal} The magnitude to round to a multiple of.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   * 'toNearest() rounding mode not an integer: {rm}'
		   * 'toNearest() rounding mode out of range: {rm}'
		   *
		   */
		  P.toNearest = function (y, rm) {
		    var x = this,
		      Ctor = x.constructor;

		    x = new Ctor(x);

		    if (y == null) {

		      // If x is not finite, return x.
		      if (!x.d) return x;

		      y = new Ctor(1);
		      rm = Ctor.rounding;
		    } else {
		      y = new Ctor(y);
		      if (rm === void 0) {
		        rm = Ctor.rounding;
		      } else {
		        checkInt32(rm, 0, 8);
		      }

		      // If x is not finite, return x if y is not NaN, else NaN.
		      if (!x.d) return y.s ? x : y;

		      // If y is not finite, return Infinity with the sign of x if y is Infinity, else NaN.
		      if (!y.d) {
		        if (y.s) y.s = x.s;
		        return y;
		      }
		    }

		    // If y is not zero, calculate the nearest multiple of y to x.
		    if (y.d[0]) {
		      external = false;
		      x = divide(x, y, 0, rm, 1).times(y);
		      external = true;
		      finalise(x);

		    // If y is zero, return zero with the sign of x.
		    } else {
		      y.s = x.s;
		      x = y;
		    }

		    return x;
		  };


		  /*
		   * Return the value of this Decimal converted to a number primitive.
		   * Zero keeps its sign.
		   *
		   */
		  P.toNumber = function () {
		    return +this;
		  };


		  /*
		   * Return a string representing the value of this Decimal in base 8, round to `sd` significant
		   * digits using rounding mode `rm`.
		   *
		   * If the optional `sd` argument is present then return binary exponential notation.
		   *
		   * [sd] {number} Significant digits. Integer, 1 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   */
		  P.toOctal = function (sd, rm) {
		    return toStringBinary(this, 8, sd, rm);
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal raised to the power `y`, rounded
		   * to `precision` significant digits using rounding mode `rounding`.
		   *
		   * ECMAScript compliant.
		   *
		   *   pow(x, NaN)                           = NaN
		   *   pow(x, ±0)                            = 1

		   *   pow(NaN, non-zero)                    = NaN
		   *   pow(abs(x) > 1, +Infinity)            = +Infinity
		   *   pow(abs(x) > 1, -Infinity)            = +0
		   *   pow(abs(x) == 1, ±Infinity)           = NaN
		   *   pow(abs(x) < 1, +Infinity)            = +0
		   *   pow(abs(x) < 1, -Infinity)            = +Infinity
		   *   pow(+Infinity, y > 0)                 = +Infinity
		   *   pow(+Infinity, y < 0)                 = +0
		   *   pow(-Infinity, odd integer > 0)       = -Infinity
		   *   pow(-Infinity, even integer > 0)      = +Infinity
		   *   pow(-Infinity, odd integer < 0)       = -0
		   *   pow(-Infinity, even integer < 0)      = +0
		   *   pow(+0, y > 0)                        = +0
		   *   pow(+0, y < 0)                        = +Infinity
		   *   pow(-0, odd integer > 0)              = -0
		   *   pow(-0, even integer > 0)             = +0
		   *   pow(-0, odd integer < 0)              = -Infinity
		   *   pow(-0, even integer < 0)             = +Infinity
		   *   pow(finite x < 0, finite non-integer) = NaN
		   *
		   * For non-integer or very large exponents pow(x, y) is calculated using
		   *
		   *   x^y = exp(y*ln(x))
		   *
		   * Assuming the first 15 rounding digits are each equally likely to be any digit 0-9, the
		   * probability of an incorrectly rounded result
		   * P([49]9{14} | [50]0{14}) = 2 * 0.2 * 10^-14 = 4e-15 = 1/2.5e+14
		   * i.e. 1 in 250,000,000,000,000
		   *
		   * If a result is incorrectly rounded the maximum error will be 1 ulp (unit in last place).
		   *
		   * y {number|string|bigint|Decimal} The power to which to raise this Decimal.
		   *
		   */
		  P.toPower = P.pow = function (y) {
		    var e, k, pr, r, rm, s,
		      x = this,
		      Ctor = x.constructor,
		      yn = +(y = new Ctor(y));

		    // Either ±Infinity, NaN or ±0?
		    if (!x.d || !y.d || !x.d[0] || !y.d[0]) return new Ctor(mathpow(+x, yn));

		    x = new Ctor(x);

		    if (x.eq(1)) return x;

		    pr = Ctor.precision;
		    rm = Ctor.rounding;

		    if (y.eq(1)) return finalise(x, pr, rm);

		    // y exponent
		    e = mathfloor(y.e / LOG_BASE);

		    // If y is a small integer use the 'exponentiation by squaring' algorithm.
		    if (e >= y.d.length - 1 && (k = yn < 0 ? -yn : yn) <= MAX_SAFE_INTEGER) {
		      r = intPow(Ctor, x, k, pr);
		      return y.s < 0 ? new Ctor(1).div(r) : finalise(r, pr, rm);
		    }

		    s = x.s;

		    // if x is negative
		    if (s < 0) {

		      // if y is not an integer
		      if (e < y.d.length - 1) return new Ctor(NaN);

		      // Result is positive if x is negative and the last digit of integer y is even.
		      if ((y.d[e] & 1) == 0) s = 1;

		      // if x.eq(-1)
		      if (x.e == 0 && x.d[0] == 1 && x.d.length == 1) {
		        x.s = s;
		        return x;
		      }
		    }

		    // Estimate result exponent.
		    // x^y = 10^e,  where e = y * log10(x)
		    // log10(x) = log10(x_significand) + x_exponent
		    // log10(x_significand) = ln(x_significand) / ln(10)
		    k = mathpow(+x, yn);
		    e = k == 0 || !isFinite(k)
		      ? mathfloor(yn * (Math.log('0.' + digitsToString(x.d)) / Math.LN10 + x.e + 1))
		      : new Ctor(k + '').e;

		    // Exponent estimate may be incorrect e.g. x: 0.999999999999999999, y: 2.29, e: 0, r.e: -1.

		    // Overflow/underflow?
		    if (e > Ctor.maxE + 1 || e < Ctor.minE - 1) return new Ctor(e > 0 ? s / 0 : 0);

		    external = false;
		    Ctor.rounding = x.s = 1;

		    // Estimate the extra guard digits needed to ensure five correct rounding digits from
		    // naturalLogarithm(x). Example of failure without these extra digits (precision: 10):
		    // new Decimal(2.32456).pow('2087987436534566.46411')
		    // should be 1.162377823e+764914905173815, but is 1.162355823e+764914905173815
		    k = Math.min(12, (e + '').length);

		    // r = x^y = exp(y*ln(x))
		    r = naturalExponential(y.times(naturalLogarithm(x, pr + k)), pr);

		    // r may be Infinity, e.g. (0.9999999999999999).pow(-1e+40)
		    if (r.d) {

		      // Truncate to the required precision plus five rounding digits.
		      r = finalise(r, pr + 5, 1);

		      // If the rounding digits are [49]9999 or [50]0000 increase the precision by 10 and recalculate
		      // the result.
		      if (checkRoundingDigits(r.d, pr, rm)) {
		        e = pr + 10;

		        // Truncate to the increased precision plus five rounding digits.
		        r = finalise(naturalExponential(y.times(naturalLogarithm(x, e + k)), e), e + 5, 1);

		        // Check for 14 nines from the 2nd rounding digit (the first rounding digit may be 4 or 9).
		        if (+digitsToString(r.d).slice(pr + 1, pr + 15) + 1 == 1e14) {
		          r = finalise(r, pr + 1, 0);
		        }
		      }
		    }

		    r.s = s;
		    external = true;
		    Ctor.rounding = rm;

		    return finalise(r, pr, rm);
		  };


		  /*
		   * Return a string representing the value of this Decimal rounded to `sd` significant digits
		   * using rounding mode `rounding`.
		   *
		   * Return exponential notation if `sd` is less than the number of digits necessary to represent
		   * the integer part of the value in normal notation.
		   *
		   * [sd] {number} Significant digits. Integer, 1 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   */
		  P.toPrecision = function (sd, rm) {
		    var str,
		      x = this,
		      Ctor = x.constructor;

		    if (sd === void 0) {
		      str = finiteToString(x, x.e <= Ctor.toExpNeg || x.e >= Ctor.toExpPos);
		    } else {
		      checkInt32(sd, 1, MAX_DIGITS);

		      if (rm === void 0) rm = Ctor.rounding;
		      else checkInt32(rm, 0, 8);

		      x = finalise(new Ctor(x), sd, rm);
		      str = finiteToString(x, sd <= x.e || x.e <= Ctor.toExpNeg, sd);
		    }

		    return x.isNeg() && !x.isZero() ? '-' + str : str;
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal rounded to a maximum of `sd`
		   * significant digits using rounding mode `rm`, or to `precision` and `rounding` respectively if
		   * omitted.
		   *
		   * [sd] {number} Significant digits. Integer, 1 to MAX_DIGITS inclusive.
		   * [rm] {number} Rounding mode. Integer, 0 to 8 inclusive.
		   *
		   * 'toSD() digits out of range: {sd}'
		   * 'toSD() digits not an integer: {sd}'
		   * 'toSD() rounding mode not an integer: {rm}'
		   * 'toSD() rounding mode out of range: {rm}'
		   *
		   */
		  P.toSignificantDigits = P.toSD = function (sd, rm) {
		    var x = this,
		      Ctor = x.constructor;

		    if (sd === void 0) {
		      sd = Ctor.precision;
		      rm = Ctor.rounding;
		    } else {
		      checkInt32(sd, 1, MAX_DIGITS);

		      if (rm === void 0) rm = Ctor.rounding;
		      else checkInt32(rm, 0, 8);
		    }

		    return finalise(new Ctor(x), sd, rm);
		  };


		  /*
		   * Return a string representing the value of this Decimal.
		   *
		   * Return exponential notation if this Decimal has a positive exponent equal to or greater than
		   * `toExpPos`, or a negative exponent equal to or less than `toExpNeg`.
		   *
		   */
		  P.toString = function () {
		    var x = this,
		      Ctor = x.constructor,
		      str = finiteToString(x, x.e <= Ctor.toExpNeg || x.e >= Ctor.toExpPos);

		    return x.isNeg() && !x.isZero() ? '-' + str : str;
		  };


		  /*
		   * Return a new Decimal whose value is the value of this Decimal truncated to a whole number.
		   *
		   */
		  P.truncated = P.trunc = function () {
		    return finalise(new this.constructor(this), this.e + 1, 1);
		  };


		  /*
		   * Return a string representing the value of this Decimal.
		   * Unlike `toString`, negative zero will include the minus sign.
		   *
		   */
		  P.valueOf = P.toJSON = function () {
		    var x = this,
		      Ctor = x.constructor,
		      str = finiteToString(x, x.e <= Ctor.toExpNeg || x.e >= Ctor.toExpPos);

		    return x.isNeg() ? '-' + str : str;
		  };


		  // Helper functions for Decimal.prototype (P) and/or Decimal methods, and their callers.


		  /*
		   *  digitsToString           P.cubeRoot, P.logarithm, P.squareRoot, P.toFraction, P.toPower,
		   *                           finiteToString, naturalExponential, naturalLogarithm
		   *  checkInt32               P.toDecimalPlaces, P.toExponential, P.toFixed, P.toNearest,
		   *                           P.toPrecision, P.toSignificantDigits, toStringBinary, random
		   *  checkRoundingDigits      P.logarithm, P.toPower, naturalExponential, naturalLogarithm
		   *  convertBase              toStringBinary, parseOther
		   *  cos                      P.cos
		   *  divide                   P.atanh, P.cubeRoot, P.dividedBy, P.dividedToIntegerBy,
		   *                           P.logarithm, P.modulo, P.squareRoot, P.tan, P.tanh, P.toFraction,
		   *                           P.toNearest, toStringBinary, naturalExponential, naturalLogarithm,
		   *                           taylorSeries, atan2, parseOther
		   *  finalise                 P.absoluteValue, P.atan, P.atanh, P.ceil, P.cos, P.cosh,
		   *                           P.cubeRoot, P.dividedToIntegerBy, P.floor, P.logarithm, P.minus,
		   *                           P.modulo, P.negated, P.plus, P.round, P.sin, P.sinh, P.squareRoot,
		   *                           P.tan, P.times, P.toDecimalPlaces, P.toExponential, P.toFixed,
		   *                           P.toNearest, P.toPower, P.toPrecision, P.toSignificantDigits,
		   *                           P.truncated, divide, getLn10, getPi, naturalExponential,
		   *                           naturalLogarithm, ceil, floor, round, trunc
		   *  finiteToString           P.toExponential, P.toFixed, P.toPrecision, P.toString, P.valueOf,
		   *                           toStringBinary
		   *  getBase10Exponent        P.minus, P.plus, P.times, parseOther
		   *  getLn10                  P.logarithm, naturalLogarithm
		   *  getPi                    P.acos, P.asin, P.atan, toLessThanHalfPi, atan2
		   *  getPrecision             P.precision, P.toFraction
		   *  getZeroString            digitsToString, finiteToString
		   *  intPow                   P.toPower, parseOther
		   *  isOdd                    toLessThanHalfPi
		   *  maxOrMin                 max, min
		   *  naturalExponential       P.naturalExponential, P.toPower
		   *  naturalLogarithm         P.acosh, P.asinh, P.atanh, P.logarithm, P.naturalLogarithm,
		   *                           P.toPower, naturalExponential
		   *  nonFiniteToString        finiteToString, toStringBinary
		   *  parseDecimal             Decimal
		   *  parseOther               Decimal
		   *  sin                      P.sin
		   *  taylorSeries             P.cosh, P.sinh, cos, sin
		   *  toLessThanHalfPi         P.cos, P.sin
		   *  toStringBinary           P.toBinary, P.toHexadecimal, P.toOctal
		   *  truncate                 intPow
		   *
		   *  Throws:                  P.logarithm, P.precision, P.toFraction, checkInt32, getLn10, getPi,
		   *                           naturalLogarithm, config, parseOther, random, Decimal
		   */


		  function digitsToString(d) {
		    var i, k, ws,
		      indexOfLastWord = d.length - 1,
		      str = '',
		      w = d[0];

		    if (indexOfLastWord > 0) {
		      str += w;
		      for (i = 1; i < indexOfLastWord; i++) {
		        ws = d[i] + '';
		        k = LOG_BASE - ws.length;
		        if (k) str += getZeroString(k);
		        str += ws;
		      }

		      w = d[i];
		      ws = w + '';
		      k = LOG_BASE - ws.length;
		      if (k) str += getZeroString(k);
		    } else if (w === 0) {
		      return '0';
		    }

		    // Remove trailing zeros of last w.
		    for (; w % 10 === 0;) w /= 10;

		    return str + w;
		  }


		  function checkInt32(i, min, max) {
		    if (i !== ~~i || i < min || i > max) {
		      throw Error(invalidArgument + i);
		    }
		  }


		  /*
		   * Check 5 rounding digits if `repeating` is null, 4 otherwise.
		   * `repeating == null` if caller is `log` or `pow`,
		   * `repeating != null` if caller is `naturalLogarithm` or `naturalExponential`.
		   */
		  function checkRoundingDigits(d, i, rm, repeating) {
		    var di, k, r, rd;

		    // Get the length of the first word of the array d.
		    for (k = d[0]; k >= 10; k /= 10) --i;

		    // Is the rounding digit in the first word of d?
		    if (--i < 0) {
		      i += LOG_BASE;
		      di = 0;
		    } else {
		      di = Math.ceil((i + 1) / LOG_BASE);
		      i %= LOG_BASE;
		    }

		    // i is the index (0 - 6) of the rounding digit.
		    // E.g. if within the word 3487563 the first rounding digit is 5,
		    // then i = 4, k = 1000, rd = 3487563 % 1000 = 563
		    k = mathpow(10, LOG_BASE - i);
		    rd = d[di] % k | 0;

		    if (repeating == null) {
		      if (i < 3) {
		        if (i == 0) rd = rd / 100 | 0;
		        else if (i == 1) rd = rd / 10 | 0;
		        r = rm < 4 && rd == 99999 || rm > 3 && rd == 49999 || rd == 50000 || rd == 0;
		      } else {
		        r = (rm < 4 && rd + 1 == k || rm > 3 && rd + 1 == k / 2) &&
		          (d[di + 1] / k / 100 | 0) == mathpow(10, i - 2) - 1 ||
		            (rd == k / 2 || rd == 0) && (d[di + 1] / k / 100 | 0) == 0;
		      }
		    } else {
		      if (i < 4) {
		        if (i == 0) rd = rd / 1000 | 0;
		        else if (i == 1) rd = rd / 100 | 0;
		        else if (i == 2) rd = rd / 10 | 0;
		        r = (repeating || rm < 4) && rd == 9999 || !repeating && rm > 3 && rd == 4999;
		      } else {
		        r = ((repeating || rm < 4) && rd + 1 == k ||
		        (!repeating && rm > 3) && rd + 1 == k / 2) &&
		          (d[di + 1] / k / 1000 | 0) == mathpow(10, i - 3) - 1;
		      }
		    }

		    return r;
		  }


		  // Convert string of `baseIn` to an array of numbers of `baseOut`.
		  // Eg. convertBase('255', 10, 16) returns [15, 15].
		  // Eg. convertBase('ff', 16, 10) returns [2, 5, 5].
		  function convertBase(str, baseIn, baseOut) {
		    var j,
		      arr = [0],
		      arrL,
		      i = 0,
		      strL = str.length;

		    for (; i < strL;) {
		      for (arrL = arr.length; arrL--;) arr[arrL] *= baseIn;
		      arr[0] += NUMERALS.indexOf(str.charAt(i++));
		      for (j = 0; j < arr.length; j++) {
		        if (arr[j] > baseOut - 1) {
		          if (arr[j + 1] === void 0) arr[j + 1] = 0;
		          arr[j + 1] += arr[j] / baseOut | 0;
		          arr[j] %= baseOut;
		        }
		      }
		    }

		    return arr.reverse();
		  }


		  /*
		   * cos(x) = 1 - x^2/2! + x^4/4! - ...
		   * |x| < pi/2
		   *
		   */
		  function cosine(Ctor, x) {
		    var k, len, y;

		    if (x.isZero()) return x;

		    // Argument reduction: cos(4x) = 8*(cos^4(x) - cos^2(x)) + 1
		    // i.e. cos(x) = 8*(cos^4(x/4) - cos^2(x/4)) + 1

		    // Estimate the optimum number of times to use the argument reduction.
		    len = x.d.length;
		    if (len < 32) {
		      k = Math.ceil(len / 3);
		      y = (1 / tinyPow(4, k)).toString();
		    } else {
		      k = 16;
		      y = '2.3283064365386962890625e-10';
		    }

		    Ctor.precision += k;

		    x = taylorSeries(Ctor, 1, x.times(y), new Ctor(1));

		    // Reverse argument reduction
		    for (var i = k; i--;) {
		      var cos2x = x.times(x);
		      x = cos2x.times(cos2x).minus(cos2x).times(8).plus(1);
		    }

		    Ctor.precision -= k;

		    return x;
		  }


		  /*
		   * Perform division in the specified base.
		   */
		  var divide = (function () {

		    // Assumes non-zero x and k, and hence non-zero result.
		    function multiplyInteger(x, k, base) {
		      var temp,
		        carry = 0,
		        i = x.length;

		      for (x = x.slice(); i--;) {
		        temp = x[i] * k + carry;
		        x[i] = temp % base | 0;
		        carry = temp / base | 0;
		      }

		      if (carry) x.unshift(carry);

		      return x;
		    }

		    function compare(a, b, aL, bL) {
		      var i, r;

		      if (aL != bL) {
		        r = aL > bL ? 1 : -1;
		      } else {
		        for (i = r = 0; i < aL; i++) {
		          if (a[i] != b[i]) {
		            r = a[i] > b[i] ? 1 : -1;
		            break;
		          }
		        }
		      }

		      return r;
		    }

		    function subtract(a, b, aL, base) {
		      var i = 0;

		      // Subtract b from a.
		      for (; aL--;) {
		        a[aL] -= i;
		        i = a[aL] < b[aL] ? 1 : 0;
		        a[aL] = i * base + a[aL] - b[aL];
		      }

		      // Remove leading zeros.
		      for (; !a[0] && a.length > 1;) a.shift();
		    }

		    return function (x, y, pr, rm, dp, base) {
		      var cmp, e, i, k, logBase, more, prod, prodL, q, qd, rem, remL, rem0, sd, t, xi, xL, yd0,
		        yL, yz,
		        Ctor = x.constructor,
		        sign = x.s == y.s ? 1 : -1,
		        xd = x.d,
		        yd = y.d;

		      // Either NaN, Infinity or 0?
		      if (!xd || !xd[0] || !yd || !yd[0]) {

		        return new Ctor(// Return NaN if either NaN, or both Infinity or 0.
		          !x.s || !y.s || (xd ? yd && xd[0] == yd[0] : !yd) ? NaN :

		          // Return ±0 if x is 0 or y is ±Infinity, or return ±Infinity as y is 0.
		          xd && xd[0] == 0 || !yd ? sign * 0 : sign / 0);
		      }

		      if (base) {
		        logBase = 1;
		        e = x.e - y.e;
		      } else {
		        base = BASE;
		        logBase = LOG_BASE;
		        e = mathfloor(x.e / logBase) - mathfloor(y.e / logBase);
		      }

		      yL = yd.length;
		      xL = xd.length;
		      q = new Ctor(sign);
		      qd = q.d = [];

		      // Result exponent may be one less than e.
		      // The digit array of a Decimal from toStringBinary may have trailing zeros.
		      for (i = 0; yd[i] == (xd[i] || 0); i++);

		      if (yd[i] > (xd[i] || 0)) e--;

		      if (pr == null) {
		        sd = pr = Ctor.precision;
		        rm = Ctor.rounding;
		      } else if (dp) {
		        sd = pr + (x.e - y.e) + 1;
		      } else {
		        sd = pr;
		      }

		      if (sd < 0) {
		        qd.push(1);
		        more = true;
		      } else {

		        // Convert precision in number of base 10 digits to base 1e7 digits.
		        sd = sd / logBase + 2 | 0;
		        i = 0;

		        // divisor < 1e7
		        if (yL == 1) {
		          k = 0;
		          yd = yd[0];
		          sd++;

		          // k is the carry.
		          for (; (i < xL || k) && sd--; i++) {
		            t = k * base + (xd[i] || 0);
		            qd[i] = t / yd | 0;
		            k = t % yd | 0;
		          }

		          more = k || i < xL;

		        // divisor >= 1e7
		        } else {

		          // Normalise xd and yd so highest order digit of yd is >= base/2
		          k = base / (yd[0] + 1) | 0;

		          if (k > 1) {
		            yd = multiplyInteger(yd, k, base);
		            xd = multiplyInteger(xd, k, base);
		            yL = yd.length;
		            xL = xd.length;
		          }

		          xi = yL;
		          rem = xd.slice(0, yL);
		          remL = rem.length;

		          // Add zeros to make remainder as long as divisor.
		          for (; remL < yL;) rem[remL++] = 0;

		          yz = yd.slice();
		          yz.unshift(0);
		          yd0 = yd[0];

		          if (yd[1] >= base / 2) ++yd0;

		          do {
		            k = 0;

		            // Compare divisor and remainder.
		            cmp = compare(yd, rem, yL, remL);

		            // If divisor < remainder.
		            if (cmp < 0) {

		              // Calculate trial digit, k.
		              rem0 = rem[0];
		              if (yL != remL) rem0 = rem0 * base + (rem[1] || 0);

		              // k will be how many times the divisor goes into the current remainder.
		              k = rem0 / yd0 | 0;

		              //  Algorithm:
		              //  1. product = divisor * trial digit (k)
		              //  2. if product > remainder: product -= divisor, k--
		              //  3. remainder -= product
		              //  4. if product was < remainder at 2:
		              //    5. compare new remainder and divisor
		              //    6. If remainder > divisor: remainder -= divisor, k++

		              if (k > 1) {
		                if (k >= base) k = base - 1;

		                // product = divisor * trial digit.
		                prod = multiplyInteger(yd, k, base);
		                prodL = prod.length;
		                remL = rem.length;

		                // Compare product and remainder.
		                cmp = compare(prod, rem, prodL, remL);

		                // product > remainder.
		                if (cmp == 1) {
		                  k--;

		                  // Subtract divisor from product.
		                  subtract(prod, yL < prodL ? yz : yd, prodL, base);
		                }
		              } else {

		                // cmp is -1.
		                // If k is 0, there is no need to compare yd and rem again below, so change cmp to 1
		                // to avoid it. If k is 1 there is a need to compare yd and rem again below.
		                if (k == 0) cmp = k = 1;
		                prod = yd.slice();
		              }

		              prodL = prod.length;
		              if (prodL < remL) prod.unshift(0);

		              // Subtract product from remainder.
		              subtract(rem, prod, remL, base);

		              // If product was < previous remainder.
		              if (cmp == -1) {
		                remL = rem.length;

		                // Compare divisor and new remainder.
		                cmp = compare(yd, rem, yL, remL);

		                // If divisor < new remainder, subtract divisor from remainder.
		                if (cmp < 1) {
		                  k++;

		                  // Subtract divisor from remainder.
		                  subtract(rem, yL < remL ? yz : yd, remL, base);
		                }
		              }

		              remL = rem.length;
		            } else if (cmp === 0) {
		              k++;
		              rem = [0];
		            }    // if cmp === 1, k will be 0

		            // Add the next digit, k, to the result array.
		            qd[i++] = k;

		            // Update the remainder.
		            if (cmp && rem[0]) {
		              rem[remL++] = xd[xi] || 0;
		            } else {
		              rem = [xd[xi]];
		              remL = 1;
		            }

		          } while ((xi++ < xL || rem[0] !== void 0) && sd--);

		          more = rem[0] !== void 0;
		        }

		        // Leading zero?
		        if (!qd[0]) qd.shift();
		      }

		      // logBase is 1 when divide is being used for base conversion.
		      if (logBase == 1) {
		        q.e = e;
		        inexact = more;
		      } else {

		        // To calculate q.e, first get the number of digits of qd[0].
		        for (i = 1, k = qd[0]; k >= 10; k /= 10) i++;
		        q.e = i + e * logBase - 1;

		        finalise(q, dp ? pr + q.e + 1 : pr, rm, more);
		      }

		      return q;
		    };
		  })();


		  /*
		   * Round `x` to `sd` significant digits using rounding mode `rm`.
		   * Check for over/under-flow.
		   */
		   function finalise(x, sd, rm, isTruncated) {
		    var digits, i, j, k, rd, roundUp, w, xd, xdi,
		      Ctor = x.constructor;

		    // Don't round if sd is null or undefined.
		    out: if (sd != null) {
		      xd = x.d;

		      // Infinity/NaN.
		      if (!xd) return x;

		      // rd: the rounding digit, i.e. the digit after the digit that may be rounded up.
		      // w: the word of xd containing rd, a base 1e7 number.
		      // xdi: the index of w within xd.
		      // digits: the number of digits of w.
		      // i: what would be the index of rd within w if all the numbers were 7 digits long (i.e. if
		      // they had leading zeros)
		      // j: if > 0, the actual index of rd within w (if < 0, rd is a leading zero).

		      // Get the length of the first word of the digits array xd.
		      for (digits = 1, k = xd[0]; k >= 10; k /= 10) digits++;
		      i = sd - digits;

		      // Is the rounding digit in the first word of xd?
		      if (i < 0) {
		        i += LOG_BASE;
		        j = sd;
		        w = xd[xdi = 0];

		        // Get the rounding digit at index j of w.
		        rd = w / mathpow(10, digits - j - 1) % 10 | 0;
		      } else {
		        xdi = Math.ceil((i + 1) / LOG_BASE);
		        k = xd.length;
		        if (xdi >= k) {
		          if (isTruncated) {

		            // Needed by `naturalExponential`, `naturalLogarithm` and `squareRoot`.
		            for (; k++ <= xdi;) xd.push(0);
		            w = rd = 0;
		            digits = 1;
		            i %= LOG_BASE;
		            j = i - LOG_BASE + 1;
		          } else {
		            break out;
		          }
		        } else {
		          w = k = xd[xdi];

		          // Get the number of digits of w.
		          for (digits = 1; k >= 10; k /= 10) digits++;

		          // Get the index of rd within w.
		          i %= LOG_BASE;

		          // Get the index of rd within w, adjusted for leading zeros.
		          // The number of leading zeros of w is given by LOG_BASE - digits.
		          j = i - LOG_BASE + digits;

		          // Get the rounding digit at index j of w.
		          rd = j < 0 ? 0 : w / mathpow(10, digits - j - 1) % 10 | 0;
		        }
		      }

		      // Are there any non-zero digits after the rounding digit?
		      isTruncated = isTruncated || sd < 0 ||
		        xd[xdi + 1] !== void 0 || (j < 0 ? w : w % mathpow(10, digits - j - 1));

		      // The expression `w % mathpow(10, digits - j - 1)` returns all the digits of w to the right
		      // of the digit at (left-to-right) index j, e.g. if w is 908714 and j is 2, the expression
		      // will give 714.

		      roundUp = rm < 4
		        ? (rd || isTruncated) && (rm == 0 || rm == (x.s < 0 ? 3 : 2))
		        : rd > 5 || rd == 5 && (rm == 4 || isTruncated || rm == 6 &&

		          // Check whether the digit to the left of the rounding digit is odd.
		          ((i > 0 ? j > 0 ? w / mathpow(10, digits - j) : 0 : xd[xdi - 1]) % 10) & 1 ||
		            rm == (x.s < 0 ? 8 : 7));

		      if (sd < 1 || !xd[0]) {
		        xd.length = 0;
		        if (roundUp) {

		          // Convert sd to decimal places.
		          sd -= x.e + 1;

		          // 1, 0.1, 0.01, 0.001, 0.0001 etc.
		          xd[0] = mathpow(10, (LOG_BASE - sd % LOG_BASE) % LOG_BASE);
		          x.e = -sd || 0;
		        } else {

		          // Zero.
		          xd[0] = x.e = 0;
		        }

		        return x;
		      }

		      // Remove excess digits.
		      if (i == 0) {
		        xd.length = xdi;
		        k = 1;
		        xdi--;
		      } else {
		        xd.length = xdi + 1;
		        k = mathpow(10, LOG_BASE - i);

		        // E.g. 56700 becomes 56000 if 7 is the rounding digit.
		        // j > 0 means i > number of leading zeros of w.
		        xd[xdi] = j > 0 ? (w / mathpow(10, digits - j) % mathpow(10, j) | 0) * k : 0;
		      }

		      if (roundUp) {
		        for (;;) {

		          // Is the digit to be rounded up in the first word of xd?
		          if (xdi == 0) {

		            // i will be the length of xd[0] before k is added.
		            for (i = 1, j = xd[0]; j >= 10; j /= 10) i++;
		            j = xd[0] += k;
		            for (k = 1; j >= 10; j /= 10) k++;

		            // if i != k the length has increased.
		            if (i != k) {
		              x.e++;
		              if (xd[0] == BASE) xd[0] = 1;
		            }

		            break;
		          } else {
		            xd[xdi] += k;
		            if (xd[xdi] != BASE) break;
		            xd[xdi--] = 0;
		            k = 1;
		          }
		        }
		      }

		      // Remove trailing zeros.
		      for (i = xd.length; xd[--i] === 0;) xd.pop();
		    }

		    if (external) {

		      // Overflow?
		      if (x.e > Ctor.maxE) {

		        // Infinity.
		        x.d = null;
		        x.e = NaN;

		      // Underflow?
		      } else if (x.e < Ctor.minE) {

		        // Zero.
		        x.e = 0;
		        x.d = [0];
		        // Ctor.underflow = true;
		      } // else Ctor.underflow = false;
		    }

		    return x;
		  }


		  function finiteToString(x, isExp, sd) {
		    if (!x.isFinite()) return nonFiniteToString(x);
		    var k,
		      e = x.e,
		      str = digitsToString(x.d),
		      len = str.length;

		    if (isExp) {
		      if (sd && (k = sd - len) > 0) {
		        str = str.charAt(0) + '.' + str.slice(1) + getZeroString(k);
		      } else if (len > 1) {
		        str = str.charAt(0) + '.' + str.slice(1);
		      }

		      str = str + (x.e < 0 ? 'e' : 'e+') + x.e;
		    } else if (e < 0) {
		      str = '0.' + getZeroString(-e - 1) + str;
		      if (sd && (k = sd - len) > 0) str += getZeroString(k);
		    } else if (e >= len) {
		      str += getZeroString(e + 1 - len);
		      if (sd && (k = sd - e - 1) > 0) str = str + '.' + getZeroString(k);
		    } else {
		      if ((k = e + 1) < len) str = str.slice(0, k) + '.' + str.slice(k);
		      if (sd && (k = sd - len) > 0) {
		        if (e + 1 === len) str += '.';
		        str += getZeroString(k);
		      }
		    }

		    return str;
		  }


		  // Calculate the base 10 exponent from the base 1e7 exponent.
		  function getBase10Exponent(digits, e) {
		    var w = digits[0];

		    // Add the number of digits of the first word of the digits array.
		    for ( e *= LOG_BASE; w >= 10; w /= 10) e++;
		    return e;
		  }


		  function getLn10(Ctor, sd, pr) {
		    if (sd > LN10_PRECISION) {

		      // Reset global state in case the exception is caught.
		      external = true;
		      if (pr) Ctor.precision = pr;
		      throw Error(precisionLimitExceeded);
		    }
		    return finalise(new Ctor(LN10), sd, 1, true);
		  }


		  function getPi(Ctor, sd, rm) {
		    if (sd > PI_PRECISION) throw Error(precisionLimitExceeded);
		    return finalise(new Ctor(PI), sd, rm, true);
		  }


		  function getPrecision(digits) {
		    var w = digits.length - 1,
		      len = w * LOG_BASE + 1;

		    w = digits[w];

		    // If non-zero...
		    if (w) {

		      // Subtract the number of trailing zeros of the last word.
		      for (; w % 10 == 0; w /= 10) len--;

		      // Add the number of digits of the first word.
		      for (w = digits[0]; w >= 10; w /= 10) len++;
		    }

		    return len;
		  }


		  function getZeroString(k) {
		    var zs = '';
		    for (; k--;) zs += '0';
		    return zs;
		  }


		  /*
		   * Return a new Decimal whose value is the value of Decimal `x` to the power `n`, where `n` is an
		   * integer of type number.
		   *
		   * Implements 'exponentiation by squaring'. Called by `pow` and `parseOther`.
		   *
		   */
		  function intPow(Ctor, x, n, pr) {
		    var isTruncated,
		      r = new Ctor(1),

		      // Max n of 9007199254740991 takes 53 loop iterations.
		      // Maximum digits array length; leaves [28, 34] guard digits.
		      k = Math.ceil(pr / LOG_BASE + 4);

		    external = false;

		    for (;;) {
		      if (n % 2) {
		        r = r.times(x);
		        if (truncate(r.d, k)) isTruncated = true;
		      }

		      n = mathfloor(n / 2);
		      if (n === 0) {

		        // To ensure correct rounding when r.d is truncated, increment the last word if it is zero.
		        n = r.d.length - 1;
		        if (isTruncated && r.d[n] === 0) ++r.d[n];
		        break;
		      }

		      x = x.times(x);
		      truncate(x.d, k);
		    }

		    external = true;

		    return r;
		  }


		  function isOdd(n) {
		    return n.d[n.d.length - 1] & 1;
		  }


		  /*
		   * Handle `max` (`n` is -1) and `min` (`n` is 1).
		   */
		  function maxOrMin(Ctor, args, n) {
		    var k, y,
		      x = new Ctor(args[0]),
		      i = 0;

		    for (; ++i < args.length;) {
		      y = new Ctor(args[i]);

		      // NaN?
		      if (!y.s) {
		        x = y;
		        break;
		      }

		      k = x.cmp(y);

		      if (k === n || k === 0 && x.s === n) {
		        x = y;
		      }
		    }

		    return x;
		  }


		  /*
		   * Return a new Decimal whose value is the natural exponential of `x` rounded to `sd` significant
		   * digits.
		   *
		   * Taylor/Maclaurin series.
		   *
		   * exp(x) = x^0/0! + x^1/1! + x^2/2! + x^3/3! + ...
		   *
		   * Argument reduction:
		   *   Repeat x = x / 32, k += 5, until |x| < 0.1
		   *   exp(x) = exp(x / 2^k)^(2^k)
		   *
		   * Previously, the argument was initially reduced by
		   * exp(x) = exp(r) * 10^k  where r = x - k * ln10, k = floor(x / ln10)
		   * to first put r in the range [0, ln10], before dividing by 32 until |x| < 0.1, but this was
		   * found to be slower than just dividing repeatedly by 32 as above.
		   *
		   * Max integer argument: exp('20723265836946413') = 6.3e+9000000000000000
		   * Min integer argument: exp('-20723265836946411') = 1.2e-9000000000000000
		   * (Math object integer min/max: Math.exp(709) = 8.2e+307, Math.exp(-745) = 5e-324)
		   *
		   *  exp(Infinity)  = Infinity
		   *  exp(-Infinity) = 0
		   *  exp(NaN)       = NaN
		   *  exp(±0)        = 1
		   *
		   *  exp(x) is non-terminating for any finite, non-zero x.
		   *
		   *  The result will always be correctly rounded.
		   *
		   */
		  function naturalExponential(x, sd) {
		    var denominator, guard, j, pow, sum, t, wpr,
		      rep = 0,
		      i = 0,
		      k = 0,
		      Ctor = x.constructor,
		      rm = Ctor.rounding,
		      pr = Ctor.precision;

		    // 0/NaN/Infinity?
		    if (!x.d || !x.d[0] || x.e > 17) {

		      return new Ctor(x.d
		        ? !x.d[0] ? 1 : x.s < 0 ? 0 : 1 / 0
		        : x.s ? x.s < 0 ? 0 : x : 0 / 0);
		    }

		    if (sd == null) {
		      external = false;
		      wpr = pr;
		    } else {
		      wpr = sd;
		    }

		    t = new Ctor(0.03125);

		    // while abs(x) >= 0.1
		    while (x.e > -2) {

		      // x = x / 2^5
		      x = x.times(t);
		      k += 5;
		    }

		    // Use 2 * log10(2^k) + 5 (empirically derived) to estimate the increase in precision
		    // necessary to ensure the first 4 rounding digits are correct.
		    guard = Math.log(mathpow(2, k)) / Math.LN10 * 2 + 5 | 0;
		    wpr += guard;
		    denominator = pow = sum = new Ctor(1);
		    Ctor.precision = wpr;

		    for (;;) {
		      pow = finalise(pow.times(x), wpr, 1);
		      denominator = denominator.times(++i);
		      t = sum.plus(divide(pow, denominator, wpr, 1));

		      if (digitsToString(t.d).slice(0, wpr) === digitsToString(sum.d).slice(0, wpr)) {
		        j = k;
		        while (j--) sum = finalise(sum.times(sum), wpr, 1);

		        // Check to see if the first 4 rounding digits are [49]999.
		        // If so, repeat the summation with a higher precision, otherwise
		        // e.g. with precision: 18, rounding: 1
		        // exp(18.404272462595034083567793919843761) = 98372560.1229999999 (should be 98372560.123)
		        // `wpr - guard` is the index of first rounding digit.
		        if (sd == null) {

		          if (rep < 3 && checkRoundingDigits(sum.d, wpr - guard, rm, rep)) {
		            Ctor.precision = wpr += 10;
		            denominator = pow = t = new Ctor(1);
		            i = 0;
		            rep++;
		          } else {
		            return finalise(sum, Ctor.precision = pr, rm, external = true);
		          }
		        } else {
		          Ctor.precision = pr;
		          return sum;
		        }
		      }

		      sum = t;
		    }
		  }


		  /*
		   * Return a new Decimal whose value is the natural logarithm of `x` rounded to `sd` significant
		   * digits.
		   *
		   *  ln(-n)        = NaN
		   *  ln(0)         = -Infinity
		   *  ln(-0)        = -Infinity
		   *  ln(1)         = 0
		   *  ln(Infinity)  = Infinity
		   *  ln(-Infinity) = NaN
		   *  ln(NaN)       = NaN
		   *
		   *  ln(n) (n != 1) is non-terminating.
		   *
		   */
		  function naturalLogarithm(y, sd) {
		    var c, c0, denominator, e, numerator, rep, sum, t, wpr, x1, x2,
		      n = 1,
		      guard = 10,
		      x = y,
		      xd = x.d,
		      Ctor = x.constructor,
		      rm = Ctor.rounding,
		      pr = Ctor.precision;

		    // Is x negative or Infinity, NaN, 0 or 1?
		    if (x.s < 0 || !xd || !xd[0] || !x.e && xd[0] == 1 && xd.length == 1) {
		      return new Ctor(xd && !xd[0] ? -1 / 0 : x.s != 1 ? NaN : xd ? 0 : x);
		    }

		    if (sd == null) {
		      external = false;
		      wpr = pr;
		    } else {
		      wpr = sd;
		    }

		    Ctor.precision = wpr += guard;
		    c = digitsToString(xd);
		    c0 = c.charAt(0);

		    if (Math.abs(e = x.e) < 1.5e15) {

		      // Argument reduction.
		      // The series converges faster the closer the argument is to 1, so using
		      // ln(a^b) = b * ln(a),   ln(a) = ln(a^b) / b
		      // multiply the argument by itself until the leading digits of the significand are 7, 8, 9,
		      // 10, 11, 12 or 13, recording the number of multiplications so the sum of the series can
		      // later be divided by this number, then separate out the power of 10 using
		      // ln(a*10^b) = ln(a) + b*ln(10).

		      // max n is 21 (gives 0.9, 1.0 or 1.1) (9e15 / 21 = 4.2e14).
		      //while (c0 < 9 && c0 != 1 || c0 == 1 && c.charAt(1) > 1) {
		      // max n is 6 (gives 0.7 - 1.3)
		      while (c0 < 7 && c0 != 1 || c0 == 1 && c.charAt(1) > 3) {
		        x = x.times(y);
		        c = digitsToString(x.d);
		        c0 = c.charAt(0);
		        n++;
		      }

		      e = x.e;

		      if (c0 > 1) {
		        x = new Ctor('0.' + c);
		        e++;
		      } else {
		        x = new Ctor(c0 + '.' + c.slice(1));
		      }
		    } else {

		      // The argument reduction method above may result in overflow if the argument y is a massive
		      // number with exponent >= 1500000000000000 (9e15 / 6 = 1.5e15), so instead recall this
		      // function using ln(x*10^e) = ln(x) + e*ln(10).
		      t = getLn10(Ctor, wpr + 2, pr).times(e + '');
		      x = naturalLogarithm(new Ctor(c0 + '.' + c.slice(1)), wpr - guard).plus(t);
		      Ctor.precision = pr;

		      return sd == null ? finalise(x, pr, rm, external = true) : x;
		    }

		    // x1 is x reduced to a value near 1.
		    x1 = x;

		    // Taylor series.
		    // ln(y) = ln((1 + x)/(1 - x)) = 2(x + x^3/3 + x^5/5 + x^7/7 + ...)
		    // where x = (y - 1)/(y + 1)    (|x| < 1)
		    sum = numerator = x = divide(x.minus(1), x.plus(1), wpr, 1);
		    x2 = finalise(x.times(x), wpr, 1);
		    denominator = 3;

		    for (;;) {
		      numerator = finalise(numerator.times(x2), wpr, 1);
		      t = sum.plus(divide(numerator, new Ctor(denominator), wpr, 1));

		      if (digitsToString(t.d).slice(0, wpr) === digitsToString(sum.d).slice(0, wpr)) {
		        sum = sum.times(2);

		        // Reverse the argument reduction. Check that e is not 0 because, besides preventing an
		        // unnecessary calculation, -0 + 0 = +0 and to ensure correct rounding -0 needs to stay -0.
		        if (e !== 0) sum = sum.plus(getLn10(Ctor, wpr + 2, pr).times(e + ''));
		        sum = divide(sum, new Ctor(n), wpr, 1);

		        // Is rm > 3 and the first 4 rounding digits 4999, or rm < 4 (or the summation has
		        // been repeated previously) and the first 4 rounding digits 9999?
		        // If so, restart the summation with a higher precision, otherwise
		        // e.g. with precision: 12, rounding: 1
		        // ln(135520028.6126091714265381533) = 18.7246299999 when it should be 18.72463.
		        // `wpr - guard` is the index of first rounding digit.
		        if (sd == null) {
		          if (checkRoundingDigits(sum.d, wpr - guard, rm, rep)) {
		            Ctor.precision = wpr += guard;
		            t = numerator = x = divide(x1.minus(1), x1.plus(1), wpr, 1);
		            x2 = finalise(x.times(x), wpr, 1);
		            denominator = rep = 1;
		          } else {
		            return finalise(sum, Ctor.precision = pr, rm, external = true);
		          }
		        } else {
		          Ctor.precision = pr;
		          return sum;
		        }
		      }

		      sum = t;
		      denominator += 2;
		    }
		  }


		  // ±Infinity, NaN.
		  function nonFiniteToString(x) {
		    // Unsigned.
		    return String(x.s * x.s / 0);
		  }


		  /*
		   * Parse the value of a new Decimal `x` from string `str`.
		   */
		  function parseDecimal(x, str) {
		    var e, i, len;

		    // TODO BigInt str: no need to check for decimal point, exponential form or leading zeros.

		    // Decimal point?
		    if ((e = str.indexOf('.')) > -1) str = str.replace('.', '');

		    // Exponential form?
		    if ((i = str.search(/e/i)) > 0) {

		      // Determine exponent.
		      if (e < 0) e = i;
		      e += +str.slice(i + 1);
		      str = str.substring(0, i);
		    } else if (e < 0) {

		      // Integer.
		      e = str.length;
		    }

		    // Determine leading zeros.
		    for (i = 0; str.charCodeAt(i) === 48; i++);

		    // Determine trailing zeros.
		    for (len = str.length; str.charCodeAt(len - 1) === 48; --len);
		    str = str.slice(i, len);

		    if (str) {
		      len -= i;
		      x.e = e = e - i - 1;
		      x.d = [];

		      // Transform base

		      // e is the base 10 exponent.
		      // i is where to slice str to get the first word of the digits array.
		      i = (e + 1) % LOG_BASE;
		      if (e < 0) i += LOG_BASE;

		      if (i < len) {
		        if (i) x.d.push(+str.slice(0, i));
		        for (len -= LOG_BASE; i < len;) x.d.push(+str.slice(i, i += LOG_BASE));
		        str = str.slice(i);
		        i = LOG_BASE - str.length;
		      } else {
		        i -= len;
		      }

		      for (; i--;) str += '0';
		      x.d.push(+str);

		      if (external) {

		        // Overflow?
		        if (x.e > x.constructor.maxE) {

		          // Infinity.
		          x.d = null;
		          x.e = NaN;

		        // Underflow?
		        } else if (x.e < x.constructor.minE) {

		          // Zero.
		          x.e = 0;
		          x.d = [0];
		          // x.constructor.underflow = true;
		        } // else x.constructor.underflow = false;
		      }
		    } else {

		      // Zero.
		      x.e = 0;
		      x.d = [0];
		    }

		    return x;
		  }


		  /*
		   * Parse the value of a new Decimal `x` from a string `str`, which is not a decimal value.
		   */
		  function parseOther(x, str) {
		    var base, Ctor, divisor, i, isFloat, len, p, xd, xe;

		    if (str.indexOf('_') > -1) {
		      str = str.replace(/(\d)_(?=\d)/g, '$1');
		      if (isDecimal.test(str)) return parseDecimal(x, str);
		    } else if (str === 'Infinity' || str === 'NaN') {
		      if (!+str) x.s = NaN;
		      x.e = NaN;
		      x.d = null;
		      return x;
		    }

		    if (isHex.test(str))  {
		      base = 16;
		      str = str.toLowerCase();
		    } else if (isBinary.test(str))  {
		      base = 2;
		    } else if (isOctal.test(str))  {
		      base = 8;
		    } else {
		      throw Error(invalidArgument + str);
		    }

		    // Is there a binary exponent part?
		    i = str.search(/p/i);

		    if (i > 0) {
		      p = +str.slice(i + 1);
		      str = str.substring(2, i);
		    } else {
		      str = str.slice(2);
		    }

		    // Convert `str` as an integer then divide the result by `base` raised to a power such that the
		    // fraction part will be restored.
		    i = str.indexOf('.');
		    isFloat = i >= 0;
		    Ctor = x.constructor;

		    if (isFloat) {
		      str = str.replace('.', '');
		      len = str.length;
		      i = len - i;

		      // log[10](16) = 1.2041... , log[10](88) = 1.9444....
		      divisor = intPow(Ctor, new Ctor(base), i, i * 2);
		    }

		    xd = convertBase(str, base, BASE);
		    xe = xd.length - 1;

		    // Remove trailing zeros.
		    for (i = xe; xd[i] === 0; --i) xd.pop();
		    if (i < 0) return new Ctor(x.s * 0);
		    x.e = getBase10Exponent(xd, xe);
		    x.d = xd;
		    external = false;

		    // At what precision to perform the division to ensure exact conversion?
		    // maxDecimalIntegerPartDigitCount = ceil(log[10](b) * otherBaseIntegerPartDigitCount)
		    // log[10](2) = 0.30103, log[10](8) = 0.90309, log[10](16) = 1.20412
		    // E.g. ceil(1.2 * 3) = 4, so up to 4 decimal digits are needed to represent 3 hex int digits.
		    // maxDecimalFractionPartDigitCount = {Hex:4|Oct:3|Bin:1} * otherBaseFractionPartDigitCount
		    // Therefore using 4 * the number of digits of str will always be enough.
		    if (isFloat) x = divide(x, divisor, len * 4);

		    // Multiply by the binary exponent part if present.
		    if (p) x = x.times(Math.abs(p) < 54 ? mathpow(2, p) : Decimal.pow(2, p));
		    external = true;

		    return x;
		  }


		  /*
		   * sin(x) = x - x^3/3! + x^5/5! - ...
		   * |x| < pi/2
		   *
		   */
		  function sine(Ctor, x) {
		    var k,
		      len = x.d.length;

		    if (len < 3) {
		      return x.isZero() ? x : taylorSeries(Ctor, 2, x, x);
		    }

		    // Argument reduction: sin(5x) = 16*sin^5(x) - 20*sin^3(x) + 5*sin(x)
		    // i.e. sin(x) = 16*sin^5(x/5) - 20*sin^3(x/5) + 5*sin(x/5)
		    // and  sin(x) = sin(x/5)(5 + sin^2(x/5)(16sin^2(x/5) - 20))

		    // Estimate the optimum number of times to use the argument reduction.
		    k = 1.4 * Math.sqrt(len);
		    k = k > 16 ? 16 : k | 0;

		    x = x.times(1 / tinyPow(5, k));
		    x = taylorSeries(Ctor, 2, x, x);

		    // Reverse argument reduction
		    var sin2_x,
		      d5 = new Ctor(5),
		      d16 = new Ctor(16),
		      d20 = new Ctor(20);
		    for (; k--;) {
		      sin2_x = x.times(x);
		      x = x.times(d5.plus(sin2_x.times(d16.times(sin2_x).minus(d20))));
		    }

		    return x;
		  }


		  // Calculate Taylor series for `cos`, `cosh`, `sin` and `sinh`.
		  function taylorSeries(Ctor, n, x, y, isHyperbolic) {
		    var j, t, u, x2,
		      pr = Ctor.precision,
		      k = Math.ceil(pr / LOG_BASE);

		    external = false;
		    x2 = x.times(x);
		    u = new Ctor(y);

		    for (;;) {
		      t = divide(u.times(x2), new Ctor(n++ * n++), pr, 1);
		      u = isHyperbolic ? y.plus(t) : y.minus(t);
		      y = divide(t.times(x2), new Ctor(n++ * n++), pr, 1);
		      t = u.plus(y);

		      if (t.d[k] !== void 0) {
		        for (j = k; t.d[j] === u.d[j] && j--;);
		        if (j == -1) break;
		      }

		      j = u;
		      u = y;
		      y = t;
		      t = j;
		    }

		    external = true;
		    t.d.length = k + 1;

		    return t;
		  }


		  // Exponent e must be positive and non-zero.
		  function tinyPow(b, e) {
		    var n = b;
		    while (--e) n *= b;
		    return n;
		  }


		  // Return the absolute value of `x` reduced to less than or equal to half pi.
		  function toLessThanHalfPi(Ctor, x) {
		    var t,
		      isNeg = x.s < 0,
		      pi = getPi(Ctor, Ctor.precision, 1),
		      halfPi = pi.times(0.5);

		    x = x.abs();

		    if (x.lte(halfPi)) {
		      quadrant = isNeg ? 4 : 1;
		      return x;
		    }

		    t = x.divToInt(pi);

		    if (t.isZero()) {
		      quadrant = isNeg ? 3 : 2;
		    } else {
		      x = x.minus(t.times(pi));

		      // 0 <= x < pi
		      if (x.lte(halfPi)) {
		        quadrant = isOdd(t) ? (isNeg ? 2 : 3) : (isNeg ? 4 : 1);
		        return x;
		      }

		      quadrant = isOdd(t) ? (isNeg ? 1 : 4) : (isNeg ? 3 : 2);
		    }

		    return x.minus(pi).abs();
		  }


		  /*
		   * Return the value of Decimal `x` as a string in base `baseOut`.
		   *
		   * If the optional `sd` argument is present include a binary exponent suffix.
		   */
		  function toStringBinary(x, baseOut, sd, rm) {
		    var base, e, i, k, len, roundUp, str, xd, y,
		      Ctor = x.constructor,
		      isExp = sd !== void 0;

		    if (isExp) {
		      checkInt32(sd, 1, MAX_DIGITS);
		      if (rm === void 0) rm = Ctor.rounding;
		      else checkInt32(rm, 0, 8);
		    } else {
		      sd = Ctor.precision;
		      rm = Ctor.rounding;
		    }

		    if (!x.isFinite()) {
		      str = nonFiniteToString(x);
		    } else {
		      str = finiteToString(x);
		      i = str.indexOf('.');

		      // Use exponential notation according to `toExpPos` and `toExpNeg`? No, but if required:
		      // maxBinaryExponent = floor((decimalExponent + 1) * log[2](10))
		      // minBinaryExponent = floor(decimalExponent * log[2](10))
		      // log[2](10) = 3.321928094887362347870319429489390175864

		      if (isExp) {
		        base = 2;
		        if (baseOut == 16) {
		          sd = sd * 4 - 3;
		        } else if (baseOut == 8) {
		          sd = sd * 3 - 2;
		        }
		      } else {
		        base = baseOut;
		      }

		      // Convert the number as an integer then divide the result by its base raised to a power such
		      // that the fraction part will be restored.

		      // Non-integer.
		      if (i >= 0) {
		        str = str.replace('.', '');
		        y = new Ctor(1);
		        y.e = str.length - i;
		        y.d = convertBase(finiteToString(y), 10, base);
		        y.e = y.d.length;
		      }

		      xd = convertBase(str, 10, base);
		      e = len = xd.length;

		      // Remove trailing zeros.
		      for (; xd[--len] == 0;) xd.pop();

		      if (!xd[0]) {
		        str = isExp ? '0p+0' : '0';
		      } else {
		        if (i < 0) {
		          e--;
		        } else {
		          x = new Ctor(x);
		          x.d = xd;
		          x.e = e;
		          x = divide(x, y, sd, rm, 0, base);
		          xd = x.d;
		          e = x.e;
		          roundUp = inexact;
		        }

		        // The rounding digit, i.e. the digit after the digit that may be rounded up.
		        i = xd[sd];
		        k = base / 2;
		        roundUp = roundUp || xd[sd + 1] !== void 0;

		        roundUp = rm < 4
		          ? (i !== void 0 || roundUp) && (rm === 0 || rm === (x.s < 0 ? 3 : 2))
		          : i > k || i === k && (rm === 4 || roundUp || rm === 6 && xd[sd - 1] & 1 ||
		            rm === (x.s < 0 ? 8 : 7));

		        xd.length = sd;

		        if (roundUp) {

		          // Rounding up may mean the previous digit has to be rounded up and so on.
		          for (; ++xd[--sd] > base - 1;) {
		            xd[sd] = 0;
		            if (!sd) {
		              ++e;
		              xd.unshift(1);
		            }
		          }
		        }

		        // Determine trailing zeros.
		        for (len = xd.length; !xd[len - 1]; --len);

		        // E.g. [4, 11, 15] becomes 4bf.
		        for (i = 0, str = ''; i < len; i++) str += NUMERALS.charAt(xd[i]);

		        // Add binary exponent suffix?
		        if (isExp) {
		          if (len > 1) {
		            if (baseOut == 16 || baseOut == 8) {
		              i = baseOut == 16 ? 4 : 3;
		              for (--len; len % i; len++) str += '0';
		              xd = convertBase(str, base, baseOut);
		              for (len = xd.length; !xd[len - 1]; --len);

		              // xd[0] will always be be 1
		              for (i = 1, str = '1.'; i < len; i++) str += NUMERALS.charAt(xd[i]);
		            } else {
		              str = str.charAt(0) + '.' + str.slice(1);
		            }
		          }

		          str =  str + (e < 0 ? 'p' : 'p+') + e;
		        } else if (e < 0) {
		          for (; ++e;) str = '0' + str;
		          str = '0.' + str;
		        } else {
		          if (++e > len) for (e -= len; e-- ;) str += '0';
		          else if (e < len) str = str.slice(0, e) + '.' + str.slice(e);
		        }
		      }

		      str = (baseOut == 16 ? '0x' : baseOut == 2 ? '0b' : baseOut == 8 ? '0o' : '') + str;
		    }

		    return x.s < 0 ? '-' + str : str;
		  }


		  // Does not strip trailing zeros.
		  function truncate(arr, len) {
		    if (arr.length > len) {
		      arr.length = len;
		      return true;
		    }
		  }


		  // Decimal methods


		  /*
		   *  abs
		   *  acos
		   *  acosh
		   *  add
		   *  asin
		   *  asinh
		   *  atan
		   *  atanh
		   *  atan2
		   *  cbrt
		   *  ceil
		   *  clamp
		   *  clone
		   *  config
		   *  cos
		   *  cosh
		   *  div
		   *  exp
		   *  floor
		   *  hypot
		   *  ln
		   *  log
		   *  log2
		   *  log10
		   *  max
		   *  min
		   *  mod
		   *  mul
		   *  pow
		   *  random
		   *  round
		   *  set
		   *  sign
		   *  sin
		   *  sinh
		   *  sqrt
		   *  sub
		   *  sum
		   *  tan
		   *  tanh
		   *  trunc
		   */


		  /*
		   * Return a new Decimal whose value is the absolute value of `x`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function abs(x) {
		    return new this(x).abs();
		  }


		  /*
		   * Return a new Decimal whose value is the arccosine in radians of `x`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function acos(x) {
		    return new this(x).acos();
		  }


		  /*
		   * Return a new Decimal whose value is the inverse of the hyperbolic cosine of `x`, rounded to
		   * `precision` significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function acosh(x) {
		    return new this(x).acosh();
		  }


		  /*
		   * Return a new Decimal whose value is the sum of `x` and `y`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   * y {number|string|bigint|Decimal}
		   *
		   */
		  function add(x, y) {
		    return new this(x).plus(y);
		  }


		  /*
		   * Return a new Decimal whose value is the arcsine in radians of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function asin(x) {
		    return new this(x).asin();
		  }


		  /*
		   * Return a new Decimal whose value is the inverse of the hyperbolic sine of `x`, rounded to
		   * `precision` significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function asinh(x) {
		    return new this(x).asinh();
		  }


		  /*
		   * Return a new Decimal whose value is the arctangent in radians of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function atan(x) {
		    return new this(x).atan();
		  }


		  /*
		   * Return a new Decimal whose value is the inverse of the hyperbolic tangent of `x`, rounded to
		   * `precision` significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function atanh(x) {
		    return new this(x).atanh();
		  }


		  /*
		   * Return a new Decimal whose value is the arctangent in radians of `y/x` in the range -pi to pi
		   * (inclusive), rounded to `precision` significant digits using rounding mode `rounding`.
		   *
		   * Domain: [-Infinity, Infinity]
		   * Range: [-pi, pi]
		   *
		   * y {number|string|bigint|Decimal} The y-coordinate.
		   * x {number|string|bigint|Decimal} The x-coordinate.
		   *
		   * atan2(±0, -0)               = ±pi
		   * atan2(±0, +0)               = ±0
		   * atan2(±0, -x)               = ±pi for x > 0
		   * atan2(±0, x)                = ±0 for x > 0
		   * atan2(-y, ±0)               = -pi/2 for y > 0
		   * atan2(y, ±0)                = pi/2 for y > 0
		   * atan2(±y, -Infinity)        = ±pi for finite y > 0
		   * atan2(±y, +Infinity)        = ±0 for finite y > 0
		   * atan2(±Infinity, x)         = ±pi/2 for finite x
		   * atan2(±Infinity, -Infinity) = ±3*pi/4
		   * atan2(±Infinity, +Infinity) = ±pi/4
		   * atan2(NaN, x) = NaN
		   * atan2(y, NaN) = NaN
		   *
		   */
		  function atan2(y, x) {
		    y = new this(y);
		    x = new this(x);
		    var r,
		      pr = this.precision,
		      rm = this.rounding,
		      wpr = pr + 4;

		    // Either NaN
		    if (!y.s || !x.s) {
		      r = new this(NaN);

		    // Both ±Infinity
		    } else if (!y.d && !x.d) {
		      r = getPi(this, wpr, 1).times(x.s > 0 ? 0.25 : 0.75);
		      r.s = y.s;

		    // x is ±Infinity or y is ±0
		    } else if (!x.d || y.isZero()) {
		      r = x.s < 0 ? getPi(this, pr, rm) : new this(0);
		      r.s = y.s;

		    // y is ±Infinity or x is ±0
		    } else if (!y.d || x.isZero()) {
		      r = getPi(this, wpr, 1).times(0.5);
		      r.s = y.s;

		    // Both non-zero and finite
		    } else if (x.s < 0) {
		      this.precision = wpr;
		      this.rounding = 1;
		      r = this.atan(divide(y, x, wpr, 1));
		      x = getPi(this, wpr, 1);
		      this.precision = pr;
		      this.rounding = rm;
		      r = y.s < 0 ? r.minus(x) : r.plus(x);
		    } else {
		      r = this.atan(divide(y, x, wpr, 1));
		    }

		    return r;
		  }


		  /*
		   * Return a new Decimal whose value is the cube root of `x`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function cbrt(x) {
		    return new this(x).cbrt();
		  }


		  /*
		   * Return a new Decimal whose value is `x` rounded to an integer using `ROUND_CEIL`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function ceil(x) {
		    return finalise(x = new this(x), x.e + 1, 2);
		  }


		  /*
		   * Return a new Decimal whose value is `x` clamped to the range delineated by `min` and `max`.
		   *
		   * x {number|string|bigint|Decimal}
		   * min {number|string|bigint|Decimal}
		   * max {number|string|bigint|Decimal}
		   *
		   */
		  function clamp(x, min, max) {
		    return new this(x).clamp(min, max);
		  }


		  /*
		   * Configure global settings for a Decimal constructor.
		   *
		   * `obj` is an object with one or more of the following properties,
		   *
		   *   precision  {number}
		   *   rounding   {number}
		   *   toExpNeg   {number}
		   *   toExpPos   {number}
		   *   maxE       {number}
		   *   minE       {number}
		   *   modulo     {number}
		   *   crypto     {boolean|number}
		   *   defaults   {true}
		   *
		   * E.g. Decimal.config({ precision: 20, rounding: 4 })
		   *
		   */
		  function config(obj) {
		    if (!obj || typeof obj !== 'object') throw Error(decimalError + 'Object expected');
		    var i, p, v,
		      useDefaults = obj.defaults === true,
		      ps = [
		        'precision', 1, MAX_DIGITS,
		        'rounding', 0, 8,
		        'toExpNeg', -EXP_LIMIT, 0,
		        'toExpPos', 0, EXP_LIMIT,
		        'maxE', 0, EXP_LIMIT,
		        'minE', -EXP_LIMIT, 0,
		        'modulo', 0, 9
		      ];

		    for (i = 0; i < ps.length; i += 3) {
		      if (p = ps[i], useDefaults) this[p] = DEFAULTS[p];
		      if ((v = obj[p]) !== void 0) {
		        if (mathfloor(v) === v && v >= ps[i + 1] && v <= ps[i + 2]) this[p] = v;
		        else throw Error(invalidArgument + p + ': ' + v);
		      }
		    }

		    if (p = 'crypto', useDefaults) this[p] = DEFAULTS[p];
		    if ((v = obj[p]) !== void 0) {
		      if (v === true || v === false || v === 0 || v === 1) {
		        if (v) {
		          if (typeof crypto != 'undefined' && crypto &&
		            (crypto.getRandomValues || crypto.randomBytes)) {
		            this[p] = true;
		          } else {
		            throw Error(cryptoUnavailable);
		          }
		        } else {
		          this[p] = false;
		        }
		      } else {
		        throw Error(invalidArgument + p + ': ' + v);
		      }
		    }

		    return this;
		  }


		  /*
		   * Return a new Decimal whose value is the cosine of `x`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function cos(x) {
		    return new this(x).cos();
		  }


		  /*
		   * Return a new Decimal whose value is the hyperbolic cosine of `x`, rounded to precision
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function cosh(x) {
		    return new this(x).cosh();
		  }


		  /*
		   * Create and return a Decimal constructor with the same configuration properties as this Decimal
		   * constructor.
		   *
		   */
		  function clone(obj) {
		    var i, p, ps;

		    /*
		     * The Decimal constructor and exported function.
		     * Return a new Decimal instance.
		     *
		     * v {number|string|bigint|Decimal} A numeric value.
		     *
		     */
		    function Decimal(v) {
		      var e, i, t,
		        x = this;

		      // Decimal called without new.
		      if (!(x instanceof Decimal)) return new Decimal(v);

		      // Retain a reference to this Decimal constructor, and shadow Decimal.prototype.constructor
		      // which points to Object.
		      x.constructor = Decimal;

		      if (isDecimalInstance(v)) {
		        x.s = v.s;

		        if (external) {
		          if (!v.d || v.e > Decimal.maxE) {

		            // Infinity.
		            x.e = NaN;
		            x.d = null;
		          } else if (v.e < Decimal.minE) {

		            // Zero.
		            x.e = 0;
		            x.d = [0];
		          } else {
		            x.e = v.e;
		            x.d = v.d.slice();
		          }
		        } else {
		          x.e = v.e;
		          x.d = v.d ? v.d.slice() : v.d;
		        }

		        return;
		      }

		      t = typeof v;

		      if (t === 'number') {
		        if (v === 0) {
		          x.s = 1 / v < 0 ? -1 : 1;
		          x.e = 0;
		          x.d = [0];
		          return;
		        }

		        if (v < 0) {
		          v = -v;
		          x.s = -1;
		        } else {
		          x.s = 1;
		        }

		        // Fast path for small integers.
		        if (v === ~~v && v < 1e7) {
		          for (e = 0, i = v; i >= 10; i /= 10) e++;

		          if (external) {
		            if (e > Decimal.maxE) {
		              x.e = NaN;
		              x.d = null;
		            } else if (e < Decimal.minE) {
		              x.e = 0;
		              x.d = [0];
		            } else {
		              x.e = e;
		              x.d = [v];
		            }
		          } else {
		            x.e = e;
		            x.d = [v];
		          }

		          return;
		        }

		        // Infinity or NaN?
		        if (v * 0 !== 0) {
		          if (!v) x.s = NaN;
		          x.e = NaN;
		          x.d = null;
		          return;
		        }

		        return parseDecimal(x, v.toString());
		      }

		      if (t === 'string') {
		        if ((i = v.charCodeAt(0)) === 45) {  // minus sign
		          v = v.slice(1);
		          x.s = -1;
		        } else {
		          if (i === 43) v = v.slice(1);  // plus sign
		          x.s = 1;
		        }

		        return isDecimal.test(v) ? parseDecimal(x, v) : parseOther(x, v);
		      }

		      if (t === 'bigint') {
		        if (v < 0) {
		          v = -v;
		          x.s = -1;
		        } else {
		          x.s = 1;
		        }

		        return parseDecimal(x, v.toString());
		      }

		      throw Error(invalidArgument + v);
		    }

		    Decimal.prototype = P;

		    Decimal.ROUND_UP = 0;
		    Decimal.ROUND_DOWN = 1;
		    Decimal.ROUND_CEIL = 2;
		    Decimal.ROUND_FLOOR = 3;
		    Decimal.ROUND_HALF_UP = 4;
		    Decimal.ROUND_HALF_DOWN = 5;
		    Decimal.ROUND_HALF_EVEN = 6;
		    Decimal.ROUND_HALF_CEIL = 7;
		    Decimal.ROUND_HALF_FLOOR = 8;
		    Decimal.EUCLID = 9;

		    Decimal.config = Decimal.set = config;
		    Decimal.clone = clone;
		    Decimal.isDecimal = isDecimalInstance;

		    Decimal.abs = abs;
		    Decimal.acos = acos;
		    Decimal.acosh = acosh;        // ES6
		    Decimal.add = add;
		    Decimal.asin = asin;
		    Decimal.asinh = asinh;        // ES6
		    Decimal.atan = atan;
		    Decimal.atanh = atanh;        // ES6
		    Decimal.atan2 = atan2;
		    Decimal.cbrt = cbrt;          // ES6
		    Decimal.ceil = ceil;
		    Decimal.clamp = clamp;
		    Decimal.cos = cos;
		    Decimal.cosh = cosh;          // ES6
		    Decimal.div = div;
		    Decimal.exp = exp;
		    Decimal.floor = floor;
		    Decimal.hypot = hypot;        // ES6
		    Decimal.ln = ln;
		    Decimal.log = log;
		    Decimal.log10 = log10;        // ES6
		    Decimal.log2 = log2;          // ES6
		    Decimal.max = max;
		    Decimal.min = min;
		    Decimal.mod = mod;
		    Decimal.mul = mul;
		    Decimal.pow = pow;
		    Decimal.random = random;
		    Decimal.round = round;
		    Decimal.sign = sign;          // ES6
		    Decimal.sin = sin;
		    Decimal.sinh = sinh;          // ES6
		    Decimal.sqrt = sqrt;
		    Decimal.sub = sub;
		    Decimal.sum = sum;
		    Decimal.tan = tan;
		    Decimal.tanh = tanh;          // ES6
		    Decimal.trunc = trunc;        // ES6

		    if (obj === void 0) obj = {};
		    if (obj) {
		      if (obj.defaults !== true) {
		        ps = ['precision', 'rounding', 'toExpNeg', 'toExpPos', 'maxE', 'minE', 'modulo', 'crypto'];
		        for (i = 0; i < ps.length;) if (!obj.hasOwnProperty(p = ps[i++])) obj[p] = this[p];
		      }
		    }

		    Decimal.config(obj);

		    return Decimal;
		  }


		  /*
		   * Return a new Decimal whose value is `x` divided by `y`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   * y {number|string|bigint|Decimal}
		   *
		   */
		  function div(x, y) {
		    return new this(x).div(y);
		  }


		  /*
		   * Return a new Decimal whose value is the natural exponential of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} The power to which to raise the base of the natural log.
		   *
		   */
		  function exp(x) {
		    return new this(x).exp();
		  }


		  /*
		   * Return a new Decimal whose value is `x` round to an integer using `ROUND_FLOOR`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function floor(x) {
		    return finalise(x = new this(x), x.e + 1, 3);
		  }


		  /*
		   * Return a new Decimal whose value is the square root of the sum of the squares of the arguments,
		   * rounded to `precision` significant digits using rounding mode `rounding`.
		   *
		   * hypot(a, b, ...) = sqrt(a^2 + b^2 + ...)
		   *
		   * arguments {number|string|bigint|Decimal}
		   *
		   */
		  function hypot() {
		    var i, n,
		      t = new this(0);

		    external = false;

		    for (i = 0; i < arguments.length;) {
		      n = new this(arguments[i++]);
		      if (!n.d) {
		        if (n.s) {
		          external = true;
		          return new this(1 / 0);
		        }
		        t = n;
		      } else if (t.d) {
		        t = t.plus(n.times(n));
		      }
		    }

		    external = true;

		    return t.sqrt();
		  }


		  /*
		   * Return true if object is a Decimal instance (where Decimal is any Decimal constructor),
		   * otherwise return false.
		   *
		   */
		  function isDecimalInstance(obj) {
		    return obj instanceof Decimal || obj && obj.toStringTag === tag || false;
		  }


		  /*
		   * Return a new Decimal whose value is the natural logarithm of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function ln(x) {
		    return new this(x).ln();
		  }


		  /*
		   * Return a new Decimal whose value is the log of `x` to the base `y`, or to base 10 if no base
		   * is specified, rounded to `precision` significant digits using rounding mode `rounding`.
		   *
		   * log[y](x)
		   *
		   * x {number|string|bigint|Decimal} The argument of the logarithm.
		   * y {number|string|bigint|Decimal} The base of the logarithm.
		   *
		   */
		  function log(x, y) {
		    return new this(x).log(y);
		  }


		  /*
		   * Return a new Decimal whose value is the base 2 logarithm of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function log2(x) {
		    return new this(x).log(2);
		  }


		  /*
		   * Return a new Decimal whose value is the base 10 logarithm of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function log10(x) {
		    return new this(x).log(10);
		  }


		  /*
		   * Return a new Decimal whose value is the maximum of the arguments.
		   *
		   * arguments {number|string|bigint|Decimal}
		   *
		   */
		  function max() {
		    return maxOrMin(this, arguments, -1);
		  }


		  /*
		   * Return a new Decimal whose value is the minimum of the arguments.
		   *
		   * arguments {number|string|bigint|Decimal}
		   *
		   */
		  function min() {
		    return maxOrMin(this, arguments, 1);
		  }


		  /*
		   * Return a new Decimal whose value is `x` modulo `y`, rounded to `precision` significant digits
		   * using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   * y {number|string|bigint|Decimal}
		   *
		   */
		  function mod(x, y) {
		    return new this(x).mod(y);
		  }


		  /*
		   * Return a new Decimal whose value is `x` multiplied by `y`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   * y {number|string|bigint|Decimal}
		   *
		   */
		  function mul(x, y) {
		    return new this(x).mul(y);
		  }


		  /*
		   * Return a new Decimal whose value is `x` raised to the power `y`, rounded to precision
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} The base.
		   * y {number|string|bigint|Decimal} The exponent.
		   *
		   */
		  function pow(x, y) {
		    return new this(x).pow(y);
		  }


		  /*
		   * Returns a new Decimal with a random value equal to or greater than 0 and less than 1, and with
		   * `sd`, or `Decimal.precision` if `sd` is omitted, significant digits (or less if trailing zeros
		   * are produced).
		   *
		   * [sd] {number} Significant digits. Integer, 0 to MAX_DIGITS inclusive.
		   *
		   */
		  function random(sd) {
		    var d, e, k, n,
		      i = 0,
		      r = new this(1),
		      rd = [];

		    if (sd === void 0) sd = this.precision;
		    else checkInt32(sd, 1, MAX_DIGITS);

		    k = Math.ceil(sd / LOG_BASE);

		    if (!this.crypto) {
		      for (; i < k;) rd[i++] = Math.random() * 1e7 | 0;

		    // Browsers supporting crypto.getRandomValues.
		    } else if (crypto.getRandomValues) {
		      d = crypto.getRandomValues(new Uint32Array(k));

		      for (; i < k;) {
		        n = d[i];

		        // 0 <= n < 4294967296
		        // Probability n >= 4.29e9, is 4967296 / 4294967296 = 0.00116 (1 in 865).
		        if (n >= 4.29e9) {
		          d[i] = crypto.getRandomValues(new Uint32Array(1))[0];
		        } else {

		          // 0 <= n <= 4289999999
		          // 0 <= (n % 1e7) <= 9999999
		          rd[i++] = n % 1e7;
		        }
		      }

		    // Node.js supporting crypto.randomBytes.
		    } else if (crypto.randomBytes) {

		      // buffer
		      d = crypto.randomBytes(k *= 4);

		      for (; i < k;) {

		        // 0 <= n < 2147483648
		        n = d[i] + (d[i + 1] << 8) + (d[i + 2] << 16) + ((d[i + 3] & 0x7f) << 24);

		        // Probability n >= 2.14e9, is 7483648 / 2147483648 = 0.0035 (1 in 286).
		        if (n >= 2.14e9) {
		          crypto.randomBytes(4).copy(d, i);
		        } else {

		          // 0 <= n <= 2139999999
		          // 0 <= (n % 1e7) <= 9999999
		          rd.push(n % 1e7);
		          i += 4;
		        }
		      }

		      i = k / 4;
		    } else {
		      throw Error(cryptoUnavailable);
		    }

		    k = rd[--i];
		    sd %= LOG_BASE;

		    // Convert trailing digits to zeros according to sd.
		    if (k && sd) {
		      n = mathpow(10, LOG_BASE - sd);
		      rd[i] = (k / n | 0) * n;
		    }

		    // Remove trailing words which are zero.
		    for (; rd[i] === 0; i--) rd.pop();

		    // Zero?
		    if (i < 0) {
		      e = 0;
		      rd = [0];
		    } else {
		      e = -1;

		      // Remove leading words which are zero and adjust exponent accordingly.
		      for (; rd[0] === 0; e -= LOG_BASE) rd.shift();

		      // Count the digits of the first word of rd to determine leading zeros.
		      for (k = 1, n = rd[0]; n >= 10; n /= 10) k++;

		      // Adjust the exponent for leading zeros of the first word of rd.
		      if (k < LOG_BASE) e -= LOG_BASE - k;
		    }

		    r.e = e;
		    r.d = rd;

		    return r;
		  }


		  /*
		   * Return a new Decimal whose value is `x` rounded to an integer using rounding mode `rounding`.
		   *
		   * To emulate `Math.round`, set rounding to 7 (ROUND_HALF_CEIL).
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function round(x) {
		    return finalise(x = new this(x), x.e + 1, this.rounding);
		  }


		  /*
		   * Return
		   *   1    if x > 0,
		   *  -1    if x < 0,
		   *   0    if x is 0,
		   *  -0    if x is -0,
		   *   NaN  otherwise
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function sign(x) {
		    x = new this(x);
		    return x.d ? (x.d[0] ? x.s : 0 * x.s) : x.s || NaN;
		  }


		  /*
		   * Return a new Decimal whose value is the sine of `x`, rounded to `precision` significant digits
		   * using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function sin(x) {
		    return new this(x).sin();
		  }


		  /*
		   * Return a new Decimal whose value is the hyperbolic sine of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function sinh(x) {
		    return new this(x).sinh();
		  }


		  /*
		   * Return a new Decimal whose value is the square root of `x`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function sqrt(x) {
		    return new this(x).sqrt();
		  }


		  /*
		   * Return a new Decimal whose value is `x` minus `y`, rounded to `precision` significant digits
		   * using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal}
		   * y {number|string|bigint|Decimal}
		   *
		   */
		  function sub(x, y) {
		    return new this(x).sub(y);
		  }


		  /*
		   * Return a new Decimal whose value is the sum of the arguments, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * Only the result is rounded, not the intermediate calculations.
		   *
		   * arguments {number|string|bigint|Decimal}
		   *
		   */
		  function sum() {
		    var i = 0,
		      args = arguments,
		      x = new this(args[i]);

		    external = false;
		    for (; x.s && ++i < args.length;) x = x.plus(args[i]);
		    external = true;

		    return finalise(x, this.precision, this.rounding);
		  }


		  /*
		   * Return a new Decimal whose value is the tangent of `x`, rounded to `precision` significant
		   * digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function tan(x) {
		    return new this(x).tan();
		  }


		  /*
		   * Return a new Decimal whose value is the hyperbolic tangent of `x`, rounded to `precision`
		   * significant digits using rounding mode `rounding`.
		   *
		   * x {number|string|bigint|Decimal} A value in radians.
		   *
		   */
		  function tanh(x) {
		    return new this(x).tanh();
		  }


		  /*
		   * Return a new Decimal whose value is `x` truncated to an integer.
		   *
		   * x {number|string|bigint|Decimal}
		   *
		   */
		  function trunc(x) {
		    return finalise(x = new this(x), x.e + 1, 1);
		  }


		  // Create and configure initial Decimal constructor.
		  Decimal = clone(DEFAULTS);
		  Decimal.prototype.constructor = Decimal;
		  Decimal['default'] = Decimal.Decimal = Decimal;

		  // Create the internal constants from their string values.
		  LN10 = new Decimal(LN10);
		  PI = new Decimal(PI);


		  // Export.


		  // AMD.
		  if (module.exports) {
		    if (typeof Symbol == 'function' && typeof Symbol.iterator == 'symbol') {
		      P[Symbol['for']('nodejs.util.inspect.custom')] = P.toString;
		      P[Symbol.toStringTag] = 'Decimal';
		    }

		    module.exports = Decimal;

		  // Browser.
		  } else {
		    if (!globalScope) {
		      globalScope = typeof self != 'undefined' && self && self.self == self ? self : window;
		    }

		    noConflict = globalScope.Decimal;
		    Decimal.noConflict = function () {
		      globalScope.Decimal = noConflict;
		      return Decimal;
		    };

		    globalScope.Decimal = Decimal;
		  }
		})(decimal); 
	} (decimal$1));
	return decimal$1.exports;
}

var hasRequiredBigNumber;

function requireBigNumber () {
	if (hasRequiredBigNumber) return BigNumber;
	hasRequiredBigNumber = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(BigNumber, "__esModule", {
	  value: true
	});
	BigNumber.createBigNumberClass = void 0;
	var _decimal = _interopRequireDefault(requireDecimal());
	var _factory = requireFactory$b();
	const name = 'BigNumber';
	const dependencies = ['?on', 'config'];
	BigNumber.createBigNumberClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    on,
	    config
	  } = _ref;
	  const BigNumber = _decimal.default.clone({
	    precision: config.precision,
	    modulo: _decimal.default.EUCLID
	  });
	  BigNumber.prototype = Object.create(BigNumber.prototype);

	  /**
	   * Attach type information
	   */
	  BigNumber.prototype.type = 'BigNumber';
	  BigNumber.prototype.isBigNumber = true;

	  /**
	   * Get a JSON representation of a BigNumber containing
	   * type information
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "BigNumber", "value": "0.2"}`
	   */
	  BigNumber.prototype.toJSON = function () {
	    return {
	      mathjs: 'BigNumber',
	      value: this.toString()
	    };
	  };

	  /**
	   * Instantiate a BigNumber from a JSON object
	   * @param {Object} json  a JSON object structured as:
	   *                       `{"mathjs": "BigNumber", "value": "0.2"}`
	   * @return {BigNumber}
	   */
	  BigNumber.fromJSON = function (json) {
	    return new BigNumber(json.value);
	  };
	  if (on) {
	    // listen for changed in the configuration, automatically apply changed precision
	    on('config', function (curr, prev) {
	      if (curr.precision !== prev.precision) {
	        BigNumber.config({
	          precision: curr.precision
	        });
	      }
	    });
	  }
	  return BigNumber;
	}, {
	  isClass: true
	});
	return BigNumber;
}

var Complex = {};

var complex$3 = {exports: {}};

complex$3.exports;

var hasRequiredComplex$4;

function requireComplex$4 () {
	if (hasRequiredComplex$4) return complex$3.exports;
	hasRequiredComplex$4 = 1;
	(function (module) {

		/**
		 *
		 * This class allows the manipulation of complex numbers.
		 * You can pass a complex number in different formats. Either as object, double, string or two integer parameters.
		 *
		 * Object form
		 * { re: <real>, im: <imaginary> }
		 * { arg: <angle>, abs: <radius> }
		 * { phi: <angle>, r: <radius> }
		 *
		 * Array / Vector form
		 * [ real, imaginary ]
		 *
		 * Double form
		 * 99.3 - Single double value
		 *
		 * String form
		 * '23.1337' - Simple real number
		 * '15+3i' - a simple complex number
		 * '3-i' - a simple complex number
		 *
		 * Example:
		 *
		 * const c = new Complex('99.3+8i');
		 * c.mul({r: 3, i: 9}).div(4.9).sub(3, 2);
		 *
		 */


		const cosh = Math.cosh || function (x) {
		  return Math.abs(x) < 1e-9 ? 1 - x : (Math.exp(x) + Math.exp(-x)) * 0.5;
		};

		const sinh = Math.sinh || function (x) {
		  return Math.abs(x) < 1e-9 ? x : (Math.exp(x) - Math.exp(-x)) * 0.5;
		};

		/**
		 * Calculates cos(x) - 1 using Taylor series if x is small (-¼π ≤ x ≤ ¼π).
		 *
		 * @param {number} x
		 * @returns {number} cos(x) - 1
		 */
		const cosm1 = function (x) {

		  const b = Math.PI / 4;
		  if (-b > x || x > b) {
		    return Math.cos(x) - 1.0;
		  }

		  /* Calculate horner form of polynomial of taylor series in Q
		  let fac = 1, alt = 1, pol = {};
		  for (let i = 0; i <= 16; i++) {
		    fac*= i || 1;
		    if (i % 2 == 0) {
		      pol[i] = new Fraction(1, alt * fac);
		      alt = -alt;
		    }
		  }
		  console.log(new Polynomial(pol).toHorner()); // (((((((1/20922789888000x^2-1/87178291200)x^2+1/479001600)x^2-1/3628800)x^2+1/40320)x^2-1/720)x^2+1/24)x^2-1/2)x^2+1
		  */

		  const xx = x * x;
		  return xx * (
		    xx * (
		      xx * (
		        xx * (
		          xx * (
		            xx * (
		              xx * (
		                xx / 20922789888000
		                - 1 / 87178291200)
		              + 1 / 479001600)
		            - 1 / 3628800)
		          + 1 / 40320)
		        - 1 / 720)
		      + 1 / 24)
		    - 1 / 2);
		};

		const hypot = function (x, y) {

		  x = Math.abs(x);
		  y = Math.abs(y);

		  // Ensure `x` is the larger value
		  if (x < y) [x, y] = [y, x];

		  // If both are below the threshold, use straightforward Pythagoras
		  if (x < 1e8) return Math.sqrt(x * x + y * y);

		  // For larger values, scale to avoid overflow
		  y /= x;
		  return x * Math.sqrt(1 + y * y);
		};

		const parser_exit = function () {
		  throw SyntaxError('Invalid Param');
		};

		/**
		 * Calculates log(sqrt(a^2+b^2)) in a way to avoid overflows
		 *
		 * @param {number} a
		 * @param {number} b
		 * @returns {number}
		 */
		function logHypot(a, b) {

		  const _a = Math.abs(a);
		  const _b = Math.abs(b);

		  if (a === 0) {
		    return Math.log(_b);
		  }

		  if (b === 0) {
		    return Math.log(_a);
		  }

		  if (_a < 3000 && _b < 3000) {
		    return Math.log(a * a + b * b) * 0.5;
		  }

		  /* I got 4 ideas to compute this property without overflow:
		   *
		   * Testing 1000000 times with random samples for a,b ∈ [1, 1000000000] against a big decimal library to get an error estimate
		   *
		   * 1. Only eliminate the square root: (OVERALL ERROR: 3.9122483030951116e-11)

		   Math.log(a * a + b * b) / 2

		   *
		   *
		   * 2. Try to use the non-overflowing pythagoras: (OVERALL ERROR: 8.889760039210159e-10)

		   const fn = function(a, b) {
		   a = Math.abs(a);
		   b = Math.abs(b);
		   let t = Math.min(a, b);
		   a = Math.max(a, b);
		   t = t / a;

		   return Math.log(a) + Math.log(1 + t * t) / 2;
		   };

		   * 3. Abuse the identity cos(atan(y/x) = x / sqrt(x^2+y^2): (OVERALL ERROR: 3.4780178737037204e-10)

		   Math.log(a / Math.cos(Math.atan2(b, a)))

		   * 4. Use 3. and apply log rules: (OVERALL ERROR: 1.2014087502620896e-9)

		   Math.log(a) - Math.log(Math.cos(Math.atan2(b, a)))

		   */

		  a = a * 0.5;
		  b = b * 0.5;

		  return 0.5 * Math.log(a * a + b * b) + Math.LN2;
		}

		const P = { 're': 0, 'im': 0 };
		const parse = function (a, b) {

		  const z = P;

		  if (a === undefined || a === null) {
		    z['re'] =
		      z['im'] = 0;
		  } else if (b !== undefined) {
		    z['re'] = a;
		    z['im'] = b;
		  } else
		    switch (typeof a) {

		      case 'object':

		        if ('im' in a && 're' in a) {
		          z['re'] = a['re'];
		          z['im'] = a['im'];
		        } else if ('abs' in a && 'arg' in a) {
		          if (!isFinite(a['abs']) && isFinite(a['arg'])) {
		            return Complex['INFINITY'];
		          }
		          z['re'] = a['abs'] * Math.cos(a['arg']);
		          z['im'] = a['abs'] * Math.sin(a['arg']);
		        } else if ('r' in a && 'phi' in a) {
		          if (!isFinite(a['r']) && isFinite(a['phi'])) {
		            return Complex['INFINITY'];
		          }
		          z['re'] = a['r'] * Math.cos(a['phi']);
		          z['im'] = a['r'] * Math.sin(a['phi']);
		        } else if (a.length === 2) { // Quick array check
		          z['re'] = a[0];
		          z['im'] = a[1];
		        } else {
		          parser_exit();
		        }
		        break;

		      case 'string':

		        z['im'] = /* void */
		        z['re'] = 0;

		        const tokens = a.replace(/_/g, '')
		          .match(/\d+\.?\d*e[+-]?\d+|\d+\.?\d*|\.\d+|./g);
		        let plus = 1;
		        let minus = 0;

		        if (tokens === null) {
		          parser_exit();
		        }

		        for (let i = 0; i < tokens.length; i++) {

		          const c = tokens[i];

		          if (c === ' ' || c === '\t' || c === '\n') ; else if (c === '+') {
		            plus++;
		          } else if (c === '-') {
		            minus++;
		          } else if (c === 'i' || c === 'I') {

		            if (plus + minus === 0) {
		              parser_exit();
		            }

		            if (tokens[i + 1] !== ' ' && !isNaN(tokens[i + 1])) {
		              z['im'] += parseFloat((minus % 2 ? '-' : '') + tokens[i + 1]);
		              i++;
		            } else {
		              z['im'] += parseFloat((minus % 2 ? '-' : '') + '1');
		            }
		            plus = minus = 0;

		          } else {

		            if (plus + minus === 0 || isNaN(c)) {
		              parser_exit();
		            }

		            if (tokens[i + 1] === 'i' || tokens[i + 1] === 'I') {
		              z['im'] += parseFloat((minus % 2 ? '-' : '') + c);
		              i++;
		            } else {
		              z['re'] += parseFloat((minus % 2 ? '-' : '') + c);
		            }
		            plus = minus = 0;
		          }
		        }

		        // Still something on the stack
		        if (plus + minus > 0) {
		          parser_exit();
		        }
		        break;

		      case 'number':
		        z['im'] = 0;
		        z['re'] = a;
		        break;

		      default:
		        parser_exit();
		    }

		  if (isNaN(z['re']) || isNaN(z['im'])) ;

		  return z;
		};

		/**
		 * @constructor
		 * @returns {Complex}
		 */
		function Complex(a, b) {

		  if (!(this instanceof Complex)) {
		    return new Complex(a, b);
		  }

		  const z = parse(a, b);

		  this['re'] = z['re'];
		  this['im'] = z['im'];
		}

		Complex.prototype = {

		  're': 0,
		  'im': 0,

		  /**
		   * Calculates the sign of a complex number, which is a normalized complex
		   *
		   * @returns {Complex}
		   */
		  'sign': function () {

		    const abs = hypot(this['re'], this['im']);

		    return new Complex(
		      this['re'] / abs,
		      this['im'] / abs);
		  },

		  /**
		   * Adds two complex numbers
		   *
		   * @returns {Complex}
		   */
		  'add': function (a, b) {

		    const z = parse(a, b);

		    const tInfin = this['isInfinite']();
		    const zInfin = !(isFinite(z['re']) && isFinite(z['im']));

		    if (tInfin || zInfin) {

		      if (tInfin && zInfin) {
		        // Infinity + Infinity = NaN
		        return Complex['NAN'];
		      }
		      // Infinity + z = Infinity { where z != Infinity }
		      return Complex['INFINITY'];
		    }

		    return new Complex(
		      this['re'] + z['re'],
		      this['im'] + z['im']);
		  },

		  /**
		   * Subtracts two complex numbers
		   *
		   * @returns {Complex}
		   */
		  'sub': function (a, b) {

		    const z = parse(a, b);

		    const tInfin = this['isInfinite']();
		    const zInfin = !(isFinite(z['re']) && isFinite(z['im']));

		    if (tInfin || zInfin) {

		      if (tInfin && zInfin) {
		        // Infinity - Infinity = NaN
		        return Complex['NAN'];
		      }
		      // Infinity - z = Infinity { where z != Infinity }
		      return Complex['INFINITY'];
		    }

		    return new Complex(
		      this['re'] - z['re'],
		      this['im'] - z['im']);
		  },

		  /**
		   * Multiplies two complex numbers
		   *
		   * @returns {Complex}
		   */
		  'mul': function (a, b) {

		    const z = parse(a, b);

		    const tInfin = this['isInfinite']();
		    const zInfin = !(isFinite(z['re']) && isFinite(z['im']));
		    const tIsZero = this['re'] === 0 && this['im'] === 0;
		    const zIsZero = z['re'] === 0 && z['im'] === 0;

		    // Infinity * 0 = NaN
		    if (tInfin && zIsZero || zInfin && tIsZero) {
		      return Complex['NAN'];
		    }

		    // Infinity * z = Infinity { where z != 0 }
		    if (tInfin || zInfin) {
		      return Complex['INFINITY'];
		    }

		    // Shortcut for real values
		    if (z['im'] === 0 && this['im'] === 0) {
		      return new Complex(this['re'] * z['re'], 0);
		    }

		    return new Complex(
		      this['re'] * z['re'] - this['im'] * z['im'],
		      this['re'] * z['im'] + this['im'] * z['re']);
		  },

		  /**
		   * Divides two complex numbers
		   *
		   * @returns {Complex}
		   */
		  'div': function (a, b) {

		    const z = parse(a, b);

		    const tInfin = this['isInfinite']();
		    const zInfin = !(isFinite(z['re']) && isFinite(z['im']));
		    const tIsZero = this['re'] === 0 && this['im'] === 0;
		    const zIsZero = z['re'] === 0 && z['im'] === 0;

		    // 0 / 0 = NaN and Infinity / Infinity = NaN
		    if (tIsZero && zIsZero || tInfin && zInfin) {
		      return Complex['NAN'];
		    }

		    // Infinity / 0 = Infinity
		    if (zIsZero || tInfin) {
		      return Complex['INFINITY'];
		    }

		    // 0 / Infinity = 0
		    if (tIsZero || zInfin) {
		      return Complex['ZERO'];
		    }

		    if (0 === z['im']) {
		      // Divisor is real
		      return new Complex(this['re'] / z['re'], this['im'] / z['re']);
		    }

		    if (Math.abs(z['re']) < Math.abs(z['im'])) {

		      const x = z['re'] / z['im'];
		      const t = z['re'] * x + z['im'];

		      return new Complex(
		        (this['re'] * x + this['im']) / t,
		        (this['im'] * x - this['re']) / t);

		    } else {

		      const x = z['im'] / z['re'];
		      const t = z['im'] * x + z['re'];

		      return new Complex(
		        (this['re'] + this['im'] * x) / t,
		        (this['im'] - this['re'] * x) / t);
		    }
		  },

		  /**
		   * Calculate the power of two complex numbers
		   *
		   * @returns {Complex}
		   */
		  'pow': function (a, b) {

		    const z = parse(a, b);

		    const tIsZero = this['re'] === 0 && this['im'] === 0;
		    const zIsZero = z['re'] === 0 && z['im'] === 0;

		    if (zIsZero) {
		      return Complex['ONE'];
		    }

		    // If the exponent is real
		    if (z['im'] === 0) {

		      if (this['im'] === 0 && this['re'] > 0) {

		        return new Complex(Math.pow(this['re'], z['re']), 0);

		      } else if (this['re'] === 0) { // If base is fully imaginary

		        switch ((z['re'] % 4 + 4) % 4) {
		          case 0:
		            return new Complex(Math.pow(this['im'], z['re']), 0);
		          case 1:
		            return new Complex(0, Math.pow(this['im'], z['re']));
		          case 2:
		            return new Complex(-Math.pow(this['im'], z['re']), 0);
		          case 3:
		            return new Complex(0, -Math.pow(this['im'], z['re']));
		        }
		      }
		    }

		    /* I couldn't find a good formula, so here is a derivation and optimization
		     *
		     * z_1^z_2 = (a + bi)^(c + di)
		     *         = exp((c + di) * log(a + bi)
		     *         = pow(a^2 + b^2, (c + di) / 2) * exp(i(c + di)atan2(b, a))
		     * =>...
		     * Re = (pow(a^2 + b^2, c / 2) * exp(-d * atan2(b, a))) * cos(d * log(a^2 + b^2) / 2 + c * atan2(b, a))
		     * Im = (pow(a^2 + b^2, c / 2) * exp(-d * atan2(b, a))) * sin(d * log(a^2 + b^2) / 2 + c * atan2(b, a))
		     *
		     * =>...
		     * Re = exp(c * log(sqrt(a^2 + b^2)) - d * atan2(b, a)) * cos(d * log(sqrt(a^2 + b^2)) + c * atan2(b, a))
		     * Im = exp(c * log(sqrt(a^2 + b^2)) - d * atan2(b, a)) * sin(d * log(sqrt(a^2 + b^2)) + c * atan2(b, a))
		     *
		     * =>
		     * Re = exp(c * logsq2 - d * arg(z_1)) * cos(d * logsq2 + c * arg(z_1))
		     * Im = exp(c * logsq2 - d * arg(z_1)) * sin(d * logsq2 + c * arg(z_1))
		     *
		     */

		    if (tIsZero && z['re'] > 0) { // Same behavior as Wolframalpha, Zero if real part is zero
		      return Complex['ZERO'];
		    }

		    const arg = Math.atan2(this['im'], this['re']);
		    const loh = logHypot(this['re'], this['im']);

		    let re = Math.exp(z['re'] * loh - z['im'] * arg);
		    let im = z['im'] * loh + z['re'] * arg;
		    return new Complex(
		      re * Math.cos(im),
		      re * Math.sin(im));
		  },

		  /**
		   * Calculate the complex square root
		   *
		   * @returns {Complex}
		   */
		  'sqrt': function () {

		    const a = this['re'];
		    const b = this['im'];

		    if (b === 0) {
		      // Real number case
		      if (a >= 0) {
		        return new Complex(Math.sqrt(a), 0);
		      } else {
		        return new Complex(0, Math.sqrt(-a));
		      }
		    }

		    const r = hypot(a, b);

		    let re = Math.sqrt(0.5 * (r + Math.abs(a))); // sqrt(2x) / 2 = sqrt(x / 2)
		    let im = Math.abs(b) / (2 * re);

		    if (a >= 0) {
		      return new Complex(re, b < 0 ? -im : im);
		    } else {
		      return new Complex(im, b < 0 ? -re : re);
		    }
		  },

		  /**
		   * Calculate the complex exponent
		   *
		   * @returns {Complex}
		   */
		  'exp': function () {

		    const er = Math.exp(this['re']);

		    if (this['im'] === 0) {
		      return new Complex(er, 0);
		    }
		    return new Complex(
		      er * Math.cos(this['im']),
		      er * Math.sin(this['im']));
		  },

		  /**
		   * Calculate the complex exponent and subtracts one.
		   *
		   * This may be more accurate than `Complex(x).exp().sub(1)` if
		   * `x` is small.
		   *
		   * @returns {Complex}
		   */
		  'expm1': function () {

		    /**
		     * exp(a + i*b) - 1
		     = exp(a) * (cos(b) + j*sin(b)) - 1
		     = expm1(a)*cos(b) + cosm1(b) + j*exp(a)*sin(b)
		     */

		    const a = this['re'];
		    const b = this['im'];

		    return new Complex(
		      Math.expm1(a) * Math.cos(b) + cosm1(b),
		      Math.exp(a) * Math.sin(b));
		  },

		  /**
		   * Calculate the natural log
		   *
		   * @returns {Complex}
		   */
		  'log': function () {

		    const a = this['re'];
		    const b = this['im'];

		    if (b === 0 && a > 0) {
		      return new Complex(Math.log(a), 0);
		    }

		    return new Complex(
		      logHypot(a, b),
		      Math.atan2(b, a));
		  },

		  /**
		   * Calculate the magnitude of the complex number
		   *
		   * @returns {number}
		   */
		  'abs': function () {

		    return hypot(this['re'], this['im']);
		  },

		  /**
		   * Calculate the angle of the complex number
		   *
		   * @returns {number}
		   */
		  'arg': function () {

		    return Math.atan2(this['im'], this['re']);
		  },

		  /**
		   * Calculate the sine of the complex number
		   *
		   * @returns {Complex}
		   */
		  'sin': function () {

		    // sin(z) = ( e^iz - e^-iz ) / 2i 
		    //        = sin(a)cosh(b) + i cos(a)sinh(b)

		    const a = this['re'];
		    const b = this['im'];

		    return new Complex(
		      Math.sin(a) * cosh(b),
		      Math.cos(a) * sinh(b));
		  },

		  /**
		   * Calculate the cosine
		   *
		   * @returns {Complex}
		   */
		  'cos': function () {

		    // cos(z) = ( e^iz + e^-iz ) / 2 
		    //        = cos(a)cosh(b) - i sin(a)sinh(b)

		    const a = this['re'];
		    const b = this['im'];

		    return new Complex(
		      Math.cos(a) * cosh(b),
		      -Math.sin(a) * sinh(b));
		  },

		  /**
		   * Calculate the tangent
		   *
		   * @returns {Complex}
		   */
		  'tan': function () {

		    // tan(z) = sin(z) / cos(z) 
		    //        = ( e^iz - e^-iz ) / ( i( e^iz + e^-iz ) )
		    //        = ( e^2iz - 1 ) / i( e^2iz + 1 )
		    //        = ( sin(2a) + i sinh(2b) ) / ( cos(2a) + cosh(2b) )

		    const a = 2 * this['re'];
		    const b = 2 * this['im'];
		    const d = Math.cos(a) + cosh(b);

		    return new Complex(
		      Math.sin(a) / d,
		      sinh(b) / d);
		  },

		  /**
		   * Calculate the cotangent
		   *
		   * @returns {Complex}
		   */
		  'cot': function () {

		    // cot(c) = i(e^(ci) + e^(-ci)) / (e^(ci) - e^(-ci))

		    const a = 2 * this['re'];
		    const b = 2 * this['im'];
		    const d = Math.cos(a) - cosh(b);

		    return new Complex(
		      -Math.sin(a) / d,
		      sinh(b) / d);
		  },

		  /**
		   * Calculate the secant
		   *
		   * @returns {Complex}
		   */
		  'sec': function () {

		    // sec(c) = 2 / (e^(ci) + e^(-ci))

		    const a = this['re'];
		    const b = this['im'];
		    const d = 0.5 * cosh(2 * b) + 0.5 * Math.cos(2 * a);

		    return new Complex(
		      Math.cos(a) * cosh(b) / d,
		      Math.sin(a) * sinh(b) / d);
		  },

		  /**
		   * Calculate the cosecans
		   *
		   * @returns {Complex}
		   */
		  'csc': function () {

		    // csc(c) = 2i / (e^(ci) - e^(-ci))

		    const a = this['re'];
		    const b = this['im'];
		    const d = 0.5 * cosh(2 * b) - 0.5 * Math.cos(2 * a);

		    return new Complex(
		      Math.sin(a) * cosh(b) / d,
		      -Math.cos(a) * sinh(b) / d);
		  },

		  /**
		   * Calculate the complex arcus sinus
		   *
		   * @returns {Complex}
		   */
		  'asin': function () {

		    // asin(c) = -i * log(ci + sqrt(1 - c^2))

		    const a = this['re'];
		    const b = this['im'];

		    const t1 = new Complex(
		      b * b - a * a + 1,
		      -2 * a * b)['sqrt']();

		    const t2 = new Complex(
		      t1['re'] - b,
		      t1['im'] + a)['log']();

		    return new Complex(t2['im'], -t2['re']);
		  },

		  /**
		   * Calculate the complex arcus cosinus
		   *
		   * @returns {Complex}
		   */
		  'acos': function () {

		    // acos(c) = i * log(c - i * sqrt(1 - c^2))

		    const a = this['re'];
		    const b = this['im'];

		    const t1 = new Complex(
		      b * b - a * a + 1,
		      -2 * a * b)['sqrt']();

		    const t2 = new Complex(
		      t1['re'] - b,
		      t1['im'] + a)['log']();

		    return new Complex(Math.PI / 2 - t2['im'], t2['re']);
		  },

		  /**
		   * Calculate the complex arcus tangent
		   *
		   * @returns {Complex}
		   */
		  'atan': function () {

		    // atan(c) = i / 2 log((i + x) / (i - x))

		    const a = this['re'];
		    const b = this['im'];

		    if (a === 0) {

		      if (b === 1) {
		        return new Complex(0, Infinity);
		      }

		      if (b === -1) {
		        return new Complex(0, -Infinity);
		      }
		    }

		    const d = a * a + (1.0 - b) * (1.0 - b);

		    const t1 = new Complex(
		      (1 - b * b - a * a) / d,
		      -2 * a / d).log();

		    return new Complex(-0.5 * t1['im'], 0.5 * t1['re']);
		  },

		  /**
		   * Calculate the complex arcus cotangent
		   *
		   * @returns {Complex}
		   */
		  'acot': function () {

		    // acot(c) = i / 2 log((c - i) / (c + i))

		    const a = this['re'];
		    const b = this['im'];

		    if (b === 0) {
		      return new Complex(Math.atan2(1, a), 0);
		    }

		    const d = a * a + b * b;
		    return (d !== 0)
		      ? new Complex(
		        a / d,
		        -b / d).atan()
		      : new Complex(
		        (a !== 0) ? a / 0 : 0,
		        (b !== 0) ? -b / 0 : 0).atan();
		  },

		  /**
		   * Calculate the complex arcus secant
		   *
		   * @returns {Complex}
		   */
		  'asec': function () {

		    // asec(c) = -i * log(1 / c + sqrt(1 - i / c^2))

		    const a = this['re'];
		    const b = this['im'];

		    if (a === 0 && b === 0) {
		      return new Complex(0, Infinity);
		    }

		    const d = a * a + b * b;
		    return (d !== 0)
		      ? new Complex(
		        a / d,
		        -b / d).acos()
		      : new Complex(
		        (a !== 0) ? a / 0 : 0,
		        (b !== 0) ? -b / 0 : 0).acos();
		  },

		  /**
		   * Calculate the complex arcus cosecans
		   *
		   * @returns {Complex}
		   */
		  'acsc': function () {

		    // acsc(c) = -i * log(i / c + sqrt(1 - 1 / c^2))

		    const a = this['re'];
		    const b = this['im'];

		    if (a === 0 && b === 0) {
		      return new Complex(Math.PI / 2, Infinity);
		    }

		    const d = a * a + b * b;
		    return (d !== 0)
		      ? new Complex(
		        a / d,
		        -b / d).asin()
		      : new Complex(
		        (a !== 0) ? a / 0 : 0,
		        (b !== 0) ? -b / 0 : 0).asin();
		  },

		  /**
		   * Calculate the complex sinh
		   *
		   * @returns {Complex}
		   */
		  'sinh': function () {

		    // sinh(c) = (e^c - e^-c) / 2

		    const a = this['re'];
		    const b = this['im'];

		    return new Complex(
		      sinh(a) * Math.cos(b),
		      cosh(a) * Math.sin(b));
		  },

		  /**
		   * Calculate the complex cosh
		   *
		   * @returns {Complex}
		   */
		  'cosh': function () {

		    // cosh(c) = (e^c + e^-c) / 2

		    const a = this['re'];
		    const b = this['im'];

		    return new Complex(
		      cosh(a) * Math.cos(b),
		      sinh(a) * Math.sin(b));
		  },

		  /**
		   * Calculate the complex tanh
		   *
		   * @returns {Complex}
		   */
		  'tanh': function () {

		    // tanh(c) = (e^c - e^-c) / (e^c + e^-c)

		    const a = 2 * this['re'];
		    const b = 2 * this['im'];
		    const d = cosh(a) + Math.cos(b);

		    return new Complex(
		      sinh(a) / d,
		      Math.sin(b) / d);
		  },

		  /**
		   * Calculate the complex coth
		   *
		   * @returns {Complex}
		   */
		  'coth': function () {

		    // coth(c) = (e^c + e^-c) / (e^c - e^-c)

		    const a = 2 * this['re'];
		    const b = 2 * this['im'];
		    const d = cosh(a) - Math.cos(b);

		    return new Complex(
		      sinh(a) / d,
		      -Math.sin(b) / d);
		  },

		  /**
		   * Calculate the complex coth
		   *
		   * @returns {Complex}
		   */
		  'csch': function () {

		    // csch(c) = 2 / (e^c - e^-c)

		    const a = this['re'];
		    const b = this['im'];
		    const d = Math.cos(2 * b) - cosh(2 * a);

		    return new Complex(
		      -2 * sinh(a) * Math.cos(b) / d,
		      2 * cosh(a) * Math.sin(b) / d);
		  },

		  /**
		   * Calculate the complex sech
		   *
		   * @returns {Complex}
		   */
		  'sech': function () {

		    // sech(c) = 2 / (e^c + e^-c)

		    const a = this['re'];
		    const b = this['im'];
		    const d = Math.cos(2 * b) + cosh(2 * a);

		    return new Complex(
		      2 * cosh(a) * Math.cos(b) / d,
		      -2 * sinh(a) * Math.sin(b) / d);
		  },

		  /**
		   * Calculate the complex asinh
		   *
		   * @returns {Complex}
		   */
		  'asinh': function () {

		    // asinh(c) = log(c + sqrt(c^2 + 1))

		    let tmp = this['im'];
		    this['im'] = -this['re'];
		    this['re'] = tmp;
		    const res = this['asin']();

		    this['re'] = -this['im'];
		    this['im'] = tmp;
		    tmp = res['re'];

		    res['re'] = -res['im'];
		    res['im'] = tmp;
		    return res;
		  },

		  /**
		   * Calculate the complex acosh
		   *
		   * @returns {Complex}
		   */
		  'acosh': function () {

		    // acosh(c) = log(c + sqrt(c^2 - 1))

		    const res = this['acos']();
		    if (res['im'] <= 0) {
		      const tmp = res['re'];
		      res['re'] = -res['im'];
		      res['im'] = tmp;
		    } else {
		      const tmp = res['im'];
		      res['im'] = -res['re'];
		      res['re'] = tmp;
		    }
		    return res;
		  },

		  /**
		   * Calculate the complex atanh
		   *
		   * @returns {Complex}
		   */
		  'atanh': function () {

		    // atanh(c) = log((1+c) / (1-c)) / 2

		    const a = this['re'];
		    const b = this['im'];

		    const noIM = a > 1 && b === 0;
		    const oneMinus = 1 - a;
		    const onePlus = 1 + a;
		    const d = oneMinus * oneMinus + b * b;

		    const x = (d !== 0)
		      ? new Complex(
		        (onePlus * oneMinus - b * b) / d,
		        (b * oneMinus + onePlus * b) / d)
		      : new Complex(
		        (a !== -1) ? (a / 0) : 0,
		        (b !== 0) ? (b / 0) : 0);

		    const temp = x['re'];
		    x['re'] = logHypot(x['re'], x['im']) / 2;
		    x['im'] = Math.atan2(x['im'], temp) / 2;
		    if (noIM) {
		      x['im'] = -x['im'];
		    }
		    return x;
		  },

		  /**
		   * Calculate the complex acoth
		   *
		   * @returns {Complex}
		   */
		  'acoth': function () {

		    // acoth(c) = log((c+1) / (c-1)) / 2

		    const a = this['re'];
		    const b = this['im'];

		    if (a === 0 && b === 0) {
		      return new Complex(0, Math.PI / 2);
		    }

		    const d = a * a + b * b;
		    return (d !== 0)
		      ? new Complex(
		        a / d,
		        -b / d).atanh()
		      : new Complex(
		        (a !== 0) ? a / 0 : 0,
		        (b !== 0) ? -b / 0 : 0).atanh();
		  },

		  /**
		   * Calculate the complex acsch
		   *
		   * @returns {Complex}
		   */
		  'acsch': function () {

		    // acsch(c) = log((1+sqrt(1+c^2))/c)

		    const a = this['re'];
		    const b = this['im'];

		    if (b === 0) {

		      return new Complex(
		        (a !== 0)
		          ? Math.log(a + Math.sqrt(a * a + 1))
		          : Infinity, 0);
		    }

		    const d = a * a + b * b;
		    return (d !== 0)
		      ? new Complex(
		        a / d,
		        -b / d).asinh()
		      : new Complex(
		        (a !== 0) ? a / 0 : 0,
		        (b !== 0) ? -b / 0 : 0).asinh();
		  },

		  /**
		   * Calculate the complex asech
		   *
		   * @returns {Complex}
		   */
		  'asech': function () {

		    // asech(c) = log((1+sqrt(1-c^2))/c)

		    const a = this['re'];
		    const b = this['im'];

		    if (this['isZero']()) {
		      return Complex['INFINITY'];
		    }

		    const d = a * a + b * b;
		    return (d !== 0)
		      ? new Complex(
		        a / d,
		        -b / d).acosh()
		      : new Complex(
		        (a !== 0) ? a / 0 : 0,
		        (b !== 0) ? -b / 0 : 0).acosh();
		  },

		  /**
		   * Calculate the complex inverse 1/z
		   *
		   * @returns {Complex}
		   */
		  'inverse': function () {

		    // 1 / 0 = Infinity and 1 / Infinity = 0
		    if (this['isZero']()) {
		      return Complex['INFINITY'];
		    }

		    if (this['isInfinite']()) {
		      return Complex['ZERO'];
		    }

		    const a = this['re'];
		    const b = this['im'];

		    const d = a * a + b * b;

		    return new Complex(a / d, -b / d);
		  },

		  /**
		   * Returns the complex conjugate
		   *
		   * @returns {Complex}
		   */
		  'conjugate': function () {

		    return new Complex(this['re'], -this['im']);
		  },

		  /**
		   * Gets the negated complex number
		   *
		   * @returns {Complex}
		   */
		  'neg': function () {

		    return new Complex(-this['re'], -this['im']);
		  },

		  /**
		   * Ceils the actual complex number
		   *
		   * @returns {Complex}
		   */
		  'ceil': function (places) {

		    places = Math.pow(10, places || 0);

		    return new Complex(
		      Math.ceil(this['re'] * places) / places,
		      Math.ceil(this['im'] * places) / places);
		  },

		  /**
		   * Floors the actual complex number
		   *
		   * @returns {Complex}
		   */
		  'floor': function (places) {

		    places = Math.pow(10, places || 0);

		    return new Complex(
		      Math.floor(this['re'] * places) / places,
		      Math.floor(this['im'] * places) / places);
		  },

		  /**
		   * Ceils the actual complex number
		   *
		   * @returns {Complex}
		   */
		  'round': function (places) {

		    places = Math.pow(10, places || 0);

		    return new Complex(
		      Math.round(this['re'] * places) / places,
		      Math.round(this['im'] * places) / places);
		  },

		  /**
		   * Compares two complex numbers
		   *
		   * **Note:** new Complex(Infinity).equals(Infinity) === false
		   *
		   * @returns {boolean}
		   */
		  'equals': function (a, b) {

		    const z = parse(a, b);

		    return Math.abs(z['re'] - this['re']) <= Complex['EPSILON'] &&
		      Math.abs(z['im'] - this['im']) <= Complex['EPSILON'];
		  },

		  /**
		   * Clones the actual object
		   *
		   * @returns {Complex}
		   */
		  'clone': function () {

		    return new Complex(this['re'], this['im']);
		  },

		  /**
		   * Gets a string of the actual complex number
		   *
		   * @returns {string}
		   */
		  'toString': function () {

		    let a = this['re'];
		    let b = this['im'];
		    let ret = "";

		    if (this['isNaN']()) {
		      return 'NaN';
		    }

		    if (this['isInfinite']()) {
		      return 'Infinity';
		    }

		    if (Math.abs(a) < Complex['EPSILON']) {
		      a = 0;
		    }

		    if (Math.abs(b) < Complex['EPSILON']) {
		      b = 0;
		    }

		    // If is real number
		    if (b === 0) {
		      return ret + a;
		    }

		    if (a !== 0) {
		      ret += a;
		      ret += " ";
		      if (b < 0) {
		        b = -b;
		        ret += "-";
		      } else {
		        ret += "+";
		      }
		      ret += " ";
		    } else if (b < 0) {
		      b = -b;
		      ret += "-";
		    }

		    if (1 !== b) { // b is the absolute imaginary part
		      ret += b;
		    }
		    return ret + "i";
		  },

		  /**
		   * Returns the actual number as a vector
		   *
		   * @returns {Array}
		   */
		  'toVector': function () {

		    return [this['re'], this['im']];
		  },

		  /**
		   * Returns the actual real value of the current object
		   *
		   * @returns {number|null}
		   */
		  'valueOf': function () {

		    if (this['im'] === 0) {
		      return this['re'];
		    }
		    return null;
		  },

		  /**
		   * Determines whether a complex number is not on the Riemann sphere.
		   *
		   * @returns {boolean}
		   */
		  'isNaN': function () {
		    return isNaN(this['re']) || isNaN(this['im']);
		  },

		  /**
		   * Determines whether or not a complex number is at the zero pole of the
		   * Riemann sphere.
		   *
		   * @returns {boolean}
		   */
		  'isZero': function () {
		    return this['im'] === 0 && this['re'] === 0;
		  },

		  /**
		   * Determines whether a complex number is not at the infinity pole of the
		   * Riemann sphere.
		   *
		   * @returns {boolean}
		   */
		  'isFinite': function () {
		    return isFinite(this['re']) && isFinite(this['im']);
		  },

		  /**
		   * Determines whether or not a complex number is at the infinity pole of the
		   * Riemann sphere.
		   *
		   * @returns {boolean}
		   */
		  'isInfinite': function () {
		    return !this['isFinite']();
		  }
		};

		Complex['ZERO'] = new Complex(0, 0);
		Complex['ONE'] = new Complex(1, 0);
		Complex['I'] = new Complex(0, 1);
		Complex['PI'] = new Complex(Math.PI, 0);
		Complex['E'] = new Complex(Math.E, 0);
		Complex['INFINITY'] = new Complex(Infinity, Infinity);
		Complex['NAN'] = new Complex(NaN, NaN);
		Complex['EPSILON'] = 1e-15;

		Object.defineProperty(Complex, "__esModule", { 'value': true });
		Complex['default'] = Complex;
		Complex['Complex'] = Complex;
		module['exports'] = Complex; 
	} (complex$3));
	return complex$3.exports;
}

var hasRequiredComplex$3;

function requireComplex$3 () {
	if (hasRequiredComplex$3) return Complex;
	hasRequiredComplex$3 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(Complex, "__esModule", {
	  value: true
	});
	Complex.createComplexClass = void 0;
	var _complex = _interopRequireDefault(requireComplex$4());
	var _number = requireNumber$3();
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'Complex';
	const dependencies = [];
	Complex.createComplexClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, () => {
	  /**
	   * Attach type information
	   */
	  Object.defineProperty(_complex.default, 'name', {
	    value: 'Complex'
	  });
	  _complex.default.prototype.constructor = _complex.default;
	  _complex.default.prototype.type = 'Complex';
	  _complex.default.prototype.isComplex = true;

	  /**
	   * Get a JSON representation of the complex number
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "Complex", "re": 2, "im": 3}`
	   */
	  _complex.default.prototype.toJSON = function () {
	    return {
	      mathjs: 'Complex',
	      re: this.re,
	      im: this.im
	    };
	  };

	  /*
	   * Return the value of the complex number in polar notation
	   * The angle phi will be set in the interval of [-pi, pi].
	   * @return {{r: number, phi: number}} Returns and object with properties r and phi.
	   */
	  _complex.default.prototype.toPolar = function () {
	    return {
	      r: this.abs(),
	      phi: this.arg()
	    };
	  };

	  /**
	   * Get a string representation of the complex number,
	   * with optional formatting options.
	   * @param {Object | number | Function} [options]  Formatting options. See
	   *                                                lib/utils/number:format for a
	   *                                                description of the available
	   *                                                options.
	   * @return {string} str
	   */
	  _complex.default.prototype.format = function (options) {
	    let str = '';
	    let im = this.im;
	    let re = this.re;
	    const strRe = (0, _number.format)(this.re, options);
	    const strIm = (0, _number.format)(this.im, options);

	    // round either re or im when smaller than the configured precision
	    const precision = (0, _is.isNumber)(options) ? options : options ? options.precision : null;
	    if (precision !== null) {
	      const epsilon = Math.pow(10, -precision);
	      if (Math.abs(re / im) < epsilon) {
	        re = 0;
	      }
	      if (Math.abs(im / re) < epsilon) {
	        im = 0;
	      }
	    }
	    if (im === 0) {
	      // real value
	      str = strRe;
	    } else if (re === 0) {
	      // purely complex value
	      if (im === 1) {
	        str = 'i';
	      } else if (im === -1) {
	        str = '-i';
	      } else {
	        str = strIm + 'i';
	      }
	    } else {
	      // complex value
	      if (im < 0) {
	        if (im === -1) {
	          str = strRe + ' - i';
	        } else {
	          str = strRe + ' - ' + strIm.substring(1) + 'i';
	        }
	      } else {
	        if (im === 1) {
	          str = strRe + ' + i';
	        } else {
	          str = strRe + ' + ' + strIm + 'i';
	        }
	      }
	    }
	    return str;
	  };

	  /**
	   * Create a complex number from polar coordinates
	   *
	   * Usage:
	   *
	   *     Complex.fromPolar(r: number, phi: number) : Complex
	   *     Complex.fromPolar({r: number, phi: number}) : Complex
	   *
	   * @param {*} args...
	   * @return {Complex}
	   */
	  _complex.default.fromPolar = function (args) {
	    switch (arguments.length) {
	      case 1:
	        {
	          const arg = arguments[0];
	          if (typeof arg === 'object') {
	            return (0, _complex.default)(arg);
	          } else {
	            throw new TypeError('Input has to be an object with r and phi keys.');
	          }
	        }
	      case 2:
	        {
	          const r = arguments[0];
	          let phi = arguments[1];
	          if ((0, _is.isNumber)(r)) {
	            if ((0, _is.isUnit)(phi) && phi.hasBase('ANGLE')) {
	              // convert unit to a number in radians
	              phi = phi.toNumber('rad');
	            }
	            if ((0, _is.isNumber)(phi)) {
	              return new _complex.default({
	                r,
	                phi
	              });
	            }
	            throw new TypeError('Phi is not a number nor an angle unit.');
	          } else {
	            throw new TypeError('Radius r is not a number.');
	          }
	        }
	      default:
	        throw new SyntaxError('Wrong number of arguments in function fromPolar');
	    }
	  };
	  _complex.default.prototype.valueOf = _complex.default.prototype.toString;

	  /**
	   * Create a Complex number from a JSON object
	   * @param {Object} json  A JSON Object structured as
	   *                       {"mathjs": "Complex", "re": 2, "im": 3}
	   *                       All properties are optional, default values
	   *                       for `re` and `im` are 0.
	   * @return {Complex} Returns a new Complex number
	   */
	  _complex.default.fromJSON = function (json) {
	    return new _complex.default(json);
	  };

	  /**
	   * Compare two complex numbers, `a` and `b`:
	   *
	   * - Returns 1 when the real part of `a` is larger than the real part of `b`
	   * - Returns -1 when the real part of `a` is smaller than the real part of `b`
	   * - Returns 1 when the real parts are equal
	   *   and the imaginary part of `a` is larger than the imaginary part of `b`
	   * - Returns -1 when the real parts are equal
	   *   and the imaginary part of `a` is smaller than the imaginary part of `b`
	   * - Returns 0 when both real and imaginary parts are equal.
	   *
	   * @params {Complex} a
	   * @params {Complex} b
	   * @returns {number} Returns the comparison result: -1, 0, or 1
	   */
	  _complex.default.compare = function (a, b) {
	    if (a.re > b.re) {
	      return 1;
	    }
	    if (a.re < b.re) {
	      return -1;
	    }
	    if (a.im > b.im) {
	      return 1;
	    }
	    if (a.im < b.im) {
	      return -1;
	    }
	    return 0;
	  };
	  return _complex.default;
	}, {
	  isClass: true
	});
	return Complex;
}

var Fraction = {};

var fraction$2 = {exports: {}};

fraction$2.exports;

var hasRequiredFraction$3;

function requireFraction$3 () {
	if (hasRequiredFraction$3) return fraction$2.exports;
	hasRequiredFraction$3 = 1;
	(function (module) {

		/**
		 *
		 * This class offers the possibility to calculate fractions.
		 * You can pass a fraction in different formats. Either as array, as double, as string or as an integer.
		 *
		 * Array/Object form
		 * [ 0 => <numerator>, 1 => <denominator> ]
		 * { n => <numerator>, d => <denominator> }
		 *
		 * Integer form
		 * - Single integer value as BigInt or Number
		 *
		 * Double form
		 * - Single double value as Number
		 *
		 * String form
		 * 123.456 - a simple double
		 * 123/456 - a string fraction
		 * 123.'456' - a double with repeating decimal places
		 * 123.(456) - synonym
		 * 123.45'6' - a double with repeating last place
		 * 123.45(6) - synonym
		 *
		 * Example:
		 * let f = new Fraction("9.4'31'");
		 * f.mul([-4, 3]).div(4.9);
		 *
		 */

		// Set Identity function to downgrade BigInt to Number if needed
		if (typeof BigInt === 'undefined') BigInt = function (n) { if (isNaN(n)) throw new Error(""); return n; };

		const C_ZERO = BigInt(0);
		const C_ONE = BigInt(1);
		const C_TWO = BigInt(2);
		const C_THREE = BigInt(3);
		const C_FIVE = BigInt(5);
		const C_TEN = BigInt(10);
		BigInt(Number.MAX_SAFE_INTEGER);

		// Maximum search depth for cyclic rational numbers. 2000 should be more than enough.
		// Example: 1/7 = 0.(142857) has 6 repeating decimal places.
		// If MAX_CYCLE_LEN gets reduced, long cycles will not be detected and toString() only gets the first 10 digits
		const MAX_CYCLE_LEN = 2000;

		// Parsed data to avoid calling "new" all the time
		const P = {
		  "s": C_ONE,
		  "n": C_ZERO,
		  "d": C_ONE
		};

		function assign(n, s) {

		  try {
		    n = BigInt(n);
		  } catch (e) {
		    throw InvalidParameter();
		  }
		  return n * s;
		}

		function ifloor(x) {
		  return typeof x === 'bigint' ? x : Math.floor(x);
		}

		// Creates a new Fraction internally without the need of the bulky constructor
		function newFraction(n, d) {

		  if (d === C_ZERO) {
		    throw DivisionByZero();
		  }

		  const f = Object.create(Fraction.prototype);
		  f["s"] = n < C_ZERO ? -C_ONE : C_ONE;

		  n = n < C_ZERO ? -n : n;

		  const a = gcd(n, d);

		  f["n"] = n / a;
		  f["d"] = d / a;
		  return f;
		}

		const FACTORSTEPS = [C_TWO * C_TWO, C_TWO, C_TWO * C_TWO, C_TWO, C_TWO * C_TWO, C_TWO * C_THREE, C_TWO, C_TWO * C_THREE]; // repeats
		function factorize(n) {

		  const factors = Object.create(null);
		  if (n <= C_ONE) {
		    factors[n] = C_ONE;
		    return factors;
		  }

		  const add = (p) => { factors[p] = (factors[p] || C_ZERO) + C_ONE; };

		  while (n % C_TWO === C_ZERO) { add(C_TWO); n /= C_TWO; }
		  while (n % C_THREE === C_ZERO) { add(C_THREE); n /= C_THREE; }
		  while (n % C_FIVE === C_ZERO) { add(C_FIVE); n /= C_FIVE; }

		  // 30-wheel trial division: test only residues coprime to 2*3*5
		  // Residue step pattern after 5: 7,11,13,17,19,23,29,31, ...
		  for (let si = 0, p = C_TWO + C_FIVE; p * p <= n;) {
		    while (n % p === C_ZERO) { add(p); n /= p; }
		    p += FACTORSTEPS[si];
		    si = (si + 1) & 7; // fast modulo 8
		  }
		  if (n > C_ONE) add(n);
		  return factors;
		}

		const parse = function (p1, p2) {

		  let n = C_ZERO, d = C_ONE, s = C_ONE;

		  if (p1 === undefined || p1 === null) ; else if (p2 !== undefined) { // Two arguments

		    if (typeof p1 === "bigint") {
		      n = p1;
		    } else if (isNaN(p1)) {
		      throw InvalidParameter();
		    } else if (p1 % 1 !== 0) {
		      throw NonIntegerParameter();
		    } else {
		      n = BigInt(p1);
		    }

		    if (typeof p2 === "bigint") {
		      d = p2;
		    } else if (isNaN(p2)) {
		      throw InvalidParameter();
		    } else if (p2 % 1 !== 0) {
		      throw NonIntegerParameter();
		    } else {
		      d = BigInt(p2);
		    }

		    s = n * d;

		  } else if (typeof p1 === "object") {
		    if ("d" in p1 && "n" in p1) {
		      n = BigInt(p1["n"]);
		      d = BigInt(p1["d"]);
		      if ("s" in p1)
		        n *= BigInt(p1["s"]);
		    } else if (0 in p1) {
		      n = BigInt(p1[0]);
		      if (1 in p1)
		        d = BigInt(p1[1]);
		    } else if (typeof p1 === "bigint") {
		      n = p1;
		    } else {
		      throw InvalidParameter();
		    }
		    s = n * d;
		  } else if (typeof p1 === "number") {

		    if (isNaN(p1)) {
		      throw InvalidParameter();
		    }

		    if (p1 < 0) {
		      s = -C_ONE;
		      p1 = -p1;
		    }

		    if (p1 % 1 === 0) {
		      n = BigInt(p1);
		    } else {

		      let z = 1;

		      let A = 0, B = 1;
		      let C = 1, D = 1;

		      let N = 10000000;

		      if (p1 >= 1) {
		        z = 10 ** Math.floor(1 + Math.log10(p1));
		        p1 /= z;
		      }

		      // Using Farey Sequences

		      while (B <= N && D <= N) {
		        let M = (A + C) / (B + D);

		        if (p1 === M) {
		          if (B + D <= N) {
		            n = A + C;
		            d = B + D;
		          } else if (D > B) {
		            n = C;
		            d = D;
		          } else {
		            n = A;
		            d = B;
		          }
		          break;

		        } else {

		          if (p1 > M) {
		            A += C;
		            B += D;
		          } else {
		            C += A;
		            D += B;
		          }

		          if (B > N) {
		            n = C;
		            d = D;
		          } else {
		            n = A;
		            d = B;
		          }
		        }
		      }
		      n = BigInt(n) * BigInt(z);
		      d = BigInt(d);
		    }

		  } else if (typeof p1 === "string") {

		    let ndx = 0;

		    let v = C_ZERO, w = C_ZERO, x = C_ZERO, y = C_ONE, z = C_ONE;

		    let match = p1.replace(/_/g, '').match(/\d+|./g);

		    if (match === null)
		      throw InvalidParameter();

		    if (match[ndx] === '-') {// Check for minus sign at the beginning
		      s = -C_ONE;
		      ndx++;
		    } else if (match[ndx] === '+') {// Check for plus sign at the beginning
		      ndx++;
		    }

		    if (match.length === ndx + 1) { // Check if it's just a simple number "1234"
		      w = assign(match[ndx++], s);
		    } else if (match[ndx + 1] === '.' || match[ndx] === '.') { // Check if it's a decimal number

		      if (match[ndx] !== '.') { // Handle 0.5 and .5
		        v = assign(match[ndx++], s);
		      }
		      ndx++;

		      // Check for decimal places
		      if (ndx + 1 === match.length || match[ndx + 1] === '(' && match[ndx + 3] === ')' || match[ndx + 1] === "'" && match[ndx + 3] === "'") {
		        w = assign(match[ndx], s);
		        y = C_TEN ** BigInt(match[ndx].length);
		        ndx++;
		      }

		      // Check for repeating places
		      if (match[ndx] === '(' && match[ndx + 2] === ')' || match[ndx] === "'" && match[ndx + 2] === "'") {
		        x = assign(match[ndx + 1], s);
		        z = C_TEN ** BigInt(match[ndx + 1].length) - C_ONE;
		        ndx += 3;
		      }

		    } else if (match[ndx + 1] === '/' || match[ndx + 1] === ':') { // Check for a simple fraction "123/456" or "123:456"
		      w = assign(match[ndx], s);
		      y = assign(match[ndx + 2], C_ONE);
		      ndx += 3;
		    } else if (match[ndx + 3] === '/' && match[ndx + 1] === ' ') { // Check for a complex fraction "123 1/2"
		      v = assign(match[ndx], s);
		      w = assign(match[ndx + 2], s);
		      y = assign(match[ndx + 4], C_ONE);
		      ndx += 5;
		    }

		    if (match.length <= ndx) { // Check for more tokens on the stack
		      d = y * z;
		      s = /* void */
		        n = x + d * v + z * w;
		    } else {
		      throw InvalidParameter();
		    }

		  } else if (typeof p1 === "bigint") {
		    n = p1;
		    s = p1;
		    d = C_ONE;
		  } else {
		    throw InvalidParameter();
		  }

		  if (d === C_ZERO) {
		    throw DivisionByZero();
		  }

		  P["s"] = s < C_ZERO ? -C_ONE : C_ONE;
		  P["n"] = n < C_ZERO ? -n : n;
		  P["d"] = d < C_ZERO ? -d : d;
		};

		function modpow(b, e, m) {

		  let r = C_ONE;
		  for (; e > C_ZERO; b = (b * b) % m, e >>= C_ONE) {

		    if (e & C_ONE) {
		      r = (r * b) % m;
		    }
		  }
		  return r;
		}

		function cycleLen(n, d) {

		  for (; d % C_TWO === C_ZERO;
		    d /= C_TWO) {
		  }

		  for (; d % C_FIVE === C_ZERO;
		    d /= C_FIVE) {
		  }

		  if (d === C_ONE) // Catch non-cyclic numbers
		    return C_ZERO;

		  // If we would like to compute really large numbers quicker, we could make use of Fermat's little theorem:
		  // 10^(d-1) % d == 1
		  // However, we don't need such large numbers and MAX_CYCLE_LEN should be the capstone,
		  // as we want to translate the numbers to strings.

		  let rem = C_TEN % d;
		  let t = 1;

		  for (; rem !== C_ONE; t++) {
		    rem = rem * C_TEN % d;

		    if (t > MAX_CYCLE_LEN)
		      return C_ZERO; // Returning 0 here means that we don't print it as a cyclic number. It's likely that the answer is `d-1`
		  }
		  return BigInt(t);
		}

		function cycleStart(n, d, len) {

		  let rem1 = C_ONE;
		  let rem2 = modpow(C_TEN, len, d);

		  for (let t = 0; t < 300; t++) { // s < ~log10(Number.MAX_VALUE)
		    // Solve 10^s == 10^(s+t) (mod d)

		    if (rem1 === rem2)
		      return BigInt(t);

		    rem1 = rem1 * C_TEN % d;
		    rem2 = rem2 * C_TEN % d;
		  }
		  return 0;
		}

		function gcd(a, b) {

		  if (!a)
		    return b;
		  if (!b)
		    return a;

		  while (1) {
		    a %= b;
		    if (!a)
		      return b;
		    b %= a;
		    if (!b)
		      return a;
		  }
		}

		/**
		 * Module constructor
		 *
		 * @constructor
		 * @param {number|Fraction=} a
		 * @param {number=} b
		 */
		function Fraction(a, b) {

		  parse(a, b);

		  if (this instanceof Fraction) {
		    a = gcd(P["d"], P["n"]); // Abuse a
		    this["s"] = P["s"];
		    this["n"] = P["n"] / a;
		    this["d"] = P["d"] / a;
		  } else {
		    return newFraction(P['s'] * P['n'], P['d']);
		  }
		}

		const DivisionByZero = function () { return new Error("Division by Zero"); };
		const InvalidParameter = function () { return new Error("Invalid argument"); };
		const NonIntegerParameter = function () { return new Error("Parameters must be integer"); };

		Fraction.prototype = {

		  "s": C_ONE,
		  "n": C_ZERO,
		  "d": C_ONE,

		  /**
		   * Calculates the absolute value
		   *
		   * Ex: new Fraction(-4).abs() => 4
		   **/
		  "abs": function () {

		    return newFraction(this["n"], this["d"]);
		  },

		  /**
		   * Inverts the sign of the current fraction
		   *
		   * Ex: new Fraction(-4).neg() => 4
		   **/
		  "neg": function () {

		    return newFraction(-this["s"] * this["n"], this["d"]);
		  },

		  /**
		   * Adds two rational numbers
		   *
		   * Ex: new Fraction({n: 2, d: 3}).add("14.9") => 467 / 30
		   **/
		  "add": function (a, b) {

		    parse(a, b);
		    return newFraction(
		      this["s"] * this["n"] * P["d"] + P["s"] * this["d"] * P["n"],
		      this["d"] * P["d"]
		    );
		  },

		  /**
		   * Subtracts two rational numbers
		   *
		   * Ex: new Fraction({n: 2, d: 3}).add("14.9") => -427 / 30
		   **/
		  "sub": function (a, b) {

		    parse(a, b);
		    return newFraction(
		      this["s"] * this["n"] * P["d"] - P["s"] * this["d"] * P["n"],
		      this["d"] * P["d"]
		    );
		  },

		  /**
		   * Multiplies two rational numbers
		   *
		   * Ex: new Fraction("-17.(345)").mul(3) => 5776 / 111
		   **/
		  "mul": function (a, b) {

		    parse(a, b);
		    return newFraction(
		      this["s"] * P["s"] * this["n"] * P["n"],
		      this["d"] * P["d"]
		    );
		  },

		  /**
		   * Divides two rational numbers
		   *
		   * Ex: new Fraction("-17.(345)").inverse().div(3)
		   **/
		  "div": function (a, b) {

		    parse(a, b);
		    return newFraction(
		      this["s"] * P["s"] * this["n"] * P["d"],
		      this["d"] * P["n"]
		    );
		  },

		  /**
		   * Clones the actual object
		   *
		   * Ex: new Fraction("-17.(345)").clone()
		   **/
		  "clone": function () {
		    return newFraction(this['s'] * this['n'], this['d']);
		  },

		  /**
		   * Calculates the modulo of two rational numbers - a more precise fmod
		   *
		   * Ex: new Fraction('4.(3)').mod([7, 8]) => (13/3) % (7/8) = (5/6)
		   * Ex: new Fraction(20, 10).mod().equals(0) ? "is Integer"
		   **/
		  "mod": function (a, b) {

		    if (a === undefined) {
		      return newFraction(this["s"] * this["n"] % this["d"], C_ONE);
		    }

		    parse(a, b);
		    if (C_ZERO === P["n"] * this["d"]) {
		      throw DivisionByZero();
		    }

		    /**
		     * I derived the rational modulo similar to the modulo for integers
		     *
		     * https://raw.org/book/analysis/rational-numbers/
		     *
		     *    n1/d1 = (n2/d2) * q + r, where 0 ≤ r < n2/d2
		     * => d2 * n1 = n2 * d1 * q + d1 * d2 * r
		     * => r = (d2 * n1 - n2 * d1 * q) / (d1 * d2)
		     *      = (d2 * n1 - n2 * d1 * floor((d2 * n1) / (n2 * d1))) / (d1 * d2)
		     *      = ((d2 * n1) % (n2 * d1)) / (d1 * d2)
		     */
		    return newFraction(
		      this["s"] * (P["d"] * this["n"]) % (P["n"] * this["d"]),
		      P["d"] * this["d"]);
		  },

		  /**
		   * Calculates the fractional gcd of two rational numbers
		   *
		   * Ex: new Fraction(5,8).gcd(3,7) => 1/56
		   */
		  "gcd": function (a, b) {

		    parse(a, b);

		    // https://raw.org/book/analysis/rational-numbers/
		    // gcd(a / b, c / d) = gcd(a, c) / lcm(b, d)

		    return newFraction(gcd(P["n"], this["n"]) * gcd(P["d"], this["d"]), P["d"] * this["d"]);
		  },

		  /**
		   * Calculates the fractional lcm of two rational numbers
		   *
		   * Ex: new Fraction(5,8).lcm(3,7) => 15
		   */
		  "lcm": function (a, b) {

		    parse(a, b);

		    // https://raw.org/book/analysis/rational-numbers/
		    // lcm(a / b, c / d) = lcm(a, c) / gcd(b, d)

		    if (P["n"] === C_ZERO && this["n"] === C_ZERO) {
		      return newFraction(C_ZERO, C_ONE);
		    }
		    return newFraction(P["n"] * this["n"], gcd(P["n"], this["n"]) * gcd(P["d"], this["d"]));
		  },

		  /**
		   * Gets the inverse of the fraction, means numerator and denominator are exchanged
		   *
		   * Ex: new Fraction([-3, 4]).inverse() => -4 / 3
		   **/
		  "inverse": function () {
		    return newFraction(this["s"] * this["d"], this["n"]);
		  },

		  /**
		   * Calculates the fraction to some integer exponent
		   *
		   * Ex: new Fraction(-1,2).pow(-3) => -8
		   */
		  "pow": function (a, b) {

		    parse(a, b);

		    // Trivial case when exp is an integer

		    if (P['d'] === C_ONE) {

		      if (P['s'] < C_ZERO) {
		        return newFraction((this['s'] * this["d"]) ** P['n'], this["n"] ** P['n']);
		      } else {
		        return newFraction((this['s'] * this["n"]) ** P['n'], this["d"] ** P['n']);
		      }
		    }

		    // Negative roots become complex
		    //     (-a/b)^(c/d) = x
		    // ⇔ (-1)^(c/d) * (a/b)^(c/d) = x
		    // ⇔ (cos(pi) + i*sin(pi))^(c/d) * (a/b)^(c/d) = x
		    // ⇔ (cos(c*pi/d) + i*sin(c*pi/d)) * (a/b)^(c/d) = x       # DeMoivre's formula
		    // From which follows that only for c=0 the root is non-complex
		    if (this['s'] < C_ZERO) return null;

		    // Now prime factor n and d
		    let N = factorize(this['n']);
		    let D = factorize(this['d']);

		    // Exponentiate and take root for n and d individually
		    let n = C_ONE;
		    let d = C_ONE;
		    for (let k in N) {
		      if (k === '1') continue;
		      if (k === '0') {
		        n = C_ZERO;
		        break;
		      }
		      N[k] *= P['n'];

		      if (N[k] % P['d'] === C_ZERO) {
		        N[k] /= P['d'];
		      } else return null;
		      n *= BigInt(k) ** N[k];
		    }

		    for (let k in D) {
		      if (k === '1') continue;
		      D[k] *= P['n'];

		      if (D[k] % P['d'] === C_ZERO) {
		        D[k] /= P['d'];
		      } else return null;
		      d *= BigInt(k) ** D[k];
		    }

		    if (P['s'] < C_ZERO) {
		      return newFraction(d, n);
		    }
		    return newFraction(n, d);
		  },

		  /**
		   * Calculates the logarithm of a fraction to a given rational base
		   *
		   * Ex: new Fraction(27, 8).log(9, 4) => 3/2
		   */
		  "log": function (a, b) {

		    parse(a, b);

		    if (this['s'] <= C_ZERO || P['s'] <= C_ZERO) return null;

		    const allPrimes = Object.create(null);

		    const baseFactors = factorize(P['n']);
		    const T1 = factorize(P['d']);

		    const numberFactors = factorize(this['n']);
		    const T2 = factorize(this['d']);

		    for (const prime in T1) {
		      baseFactors[prime] = (baseFactors[prime] || C_ZERO) - T1[prime];
		    }
		    for (const prime in T2) {
		      numberFactors[prime] = (numberFactors[prime] || C_ZERO) - T2[prime];
		    }

		    for (const prime in baseFactors) {
		      if (prime === '1') continue;
		      allPrimes[prime] = true;
		    }
		    for (const prime in numberFactors) {
		      if (prime === '1') continue;
		      allPrimes[prime] = true;
		    }

		    let retN = null;
		    let retD = null;

		    // Iterate over all unique primes to determine if a consistent ratio exists
		    for (const prime in allPrimes) {

		      const baseExponent = baseFactors[prime] || C_ZERO;
		      const numberExponent = numberFactors[prime] || C_ZERO;

		      if (baseExponent === C_ZERO) {
		        if (numberExponent !== C_ZERO) {
		          return null; // Logarithm cannot be expressed as a rational number
		        }
		        continue; // Skip this prime since both exponents are zero
		      }

		      // Calculate the ratio of exponents for this prime
		      let curN = numberExponent;
		      let curD = baseExponent;

		      // Simplify the current ratio
		      const gcdValue = gcd(curN, curD);
		      curN /= gcdValue;
		      curD /= gcdValue;

		      // Check if this is the first ratio; otherwise, ensure ratios are consistent
		      if (retN === null && retD === null) {
		        retN = curN;
		        retD = curD;
		      } else if (curN * retD !== retN * curD) {
		        return null; // Ratios do not match, logarithm cannot be rational
		      }
		    }

		    return retN !== null && retD !== null
		      ? newFraction(retN, retD)
		      : null;
		  },

		  /**
		   * Check if two rational numbers are the same
		   *
		   * Ex: new Fraction(19.6).equals([98, 5]);
		   **/
		  "equals": function (a, b) {

		    parse(a, b);
		    return this["s"] * this["n"] * P["d"] === P["s"] * P["n"] * this["d"];
		  },

		  /**
		   * Check if this rational number is less than another
		   *
		   * Ex: new Fraction(19.6).lt([98, 5]);
		   **/
		  "lt": function (a, b) {

		    parse(a, b);
		    return this["s"] * this["n"] * P["d"] < P["s"] * P["n"] * this["d"];
		  },

		  /**
		   * Check if this rational number is less than or equal another
		   *
		   * Ex: new Fraction(19.6).lt([98, 5]);
		   **/
		  "lte": function (a, b) {

		    parse(a, b);
		    return this["s"] * this["n"] * P["d"] <= P["s"] * P["n"] * this["d"];
		  },

		  /**
		   * Check if this rational number is greater than another
		   *
		   * Ex: new Fraction(19.6).lt([98, 5]);
		   **/
		  "gt": function (a, b) {

		    parse(a, b);
		    return this["s"] * this["n"] * P["d"] > P["s"] * P["n"] * this["d"];
		  },

		  /**
		   * Check if this rational number is greater than or equal another
		   *
		   * Ex: new Fraction(19.6).lt([98, 5]);
		   **/
		  "gte": function (a, b) {

		    parse(a, b);
		    return this["s"] * this["n"] * P["d"] >= P["s"] * P["n"] * this["d"];
		  },

		  /**
		   * Compare two rational numbers
		   * < 0 iff this < that
		   * > 0 iff this > that
		   * = 0 iff this = that
		   *
		   * Ex: new Fraction(19.6).compare([98, 5]);
		   **/
		  "compare": function (a, b) {

		    parse(a, b);
		    let t = this["s"] * this["n"] * P["d"] - P["s"] * P["n"] * this["d"];

		    return (C_ZERO < t) - (t < C_ZERO);
		  },

		  /**
		   * Calculates the ceil of a rational number
		   *
		   * Ex: new Fraction('4.(3)').ceil() => (5 / 1)
		   **/
		  "ceil": function (places) {

		    places = C_TEN ** BigInt(places || 0);

		    return newFraction(ifloor(this["s"] * places * this["n"] / this["d"]) +
		      (places * this["n"] % this["d"] > C_ZERO && this["s"] >= C_ZERO ? C_ONE : C_ZERO),
		      places);
		  },

		  /**
		   * Calculates the floor of a rational number
		   *
		   * Ex: new Fraction('4.(3)').floor() => (4 / 1)
		   **/
		  "floor": function (places) {

		    places = C_TEN ** BigInt(places || 0);

		    return newFraction(ifloor(this["s"] * places * this["n"] / this["d"]) -
		      (places * this["n"] % this["d"] > C_ZERO && this["s"] < C_ZERO ? C_ONE : C_ZERO),
		      places);
		  },

		  /**
		   * Rounds a rational numbers
		   *
		   * Ex: new Fraction('4.(3)').round() => (4 / 1)
		   **/
		  "round": function (places) {

		    places = C_TEN ** BigInt(places || 0);

		    /* Derivation:

		    s >= 0:
		      round(n / d) = ifloor(n / d) + (n % d) / d >= 0.5 ? 1 : 0
		                   = ifloor(n / d) + 2(n % d) >= d ? 1 : 0
		    s < 0:
		      round(n / d) =-ifloor(n / d) - (n % d) / d > 0.5 ? 1 : 0
		                   =-ifloor(n / d) - 2(n % d) > d ? 1 : 0

		    =>:

		    round(s * n / d) = s * ifloor(n / d) + s * (C + 2(n % d) > d ? 1 : 0)
		        where C = s >= 0 ? 1 : 0, to fix the >= for the positve case.
		    */

		    return newFraction(ifloor(this["s"] * places * this["n"] / this["d"]) +
		      this["s"] * ((this["s"] >= C_ZERO ? C_ONE : C_ZERO) + C_TWO * (places * this["n"] % this["d"]) > this["d"] ? C_ONE : C_ZERO),
		      places);
		  },

		  /**
		    * Rounds a rational number to a multiple of another rational number
		    *
		    * Ex: new Fraction('0.9').roundTo("1/8") => 7 / 8
		    **/
		  "roundTo": function (a, b) {

		    /*
		    k * x/y ≤ a/b < (k+1) * x/y
		    ⇔ k ≤ a/b / (x/y) < (k+1)
		    ⇔ k = floor(a/b * y/x)
		    ⇔ k = floor((a * y) / (b * x))
		    */

		    parse(a, b);

		    const n = this['n'] * P['d'];
		    const d = this['d'] * P['n'];
		    const r = n % d;

		    // round(n / d) = ifloor(n / d) + 2(n % d) >= d ? 1 : 0
		    let k = ifloor(n / d);
		    if (r + r >= d) {
		      k++;
		    }
		    return newFraction(this['s'] * k * P['n'], P['d']);
		  },

		  /**
		   * Check if two rational numbers are divisible
		   *
		   * Ex: new Fraction(19.6).divisible(1.5);
		   */
		  "divisible": function (a, b) {

		    parse(a, b);
		    if (P['n'] === C_ZERO) return false;
		    return (this['n'] * P['d']) % (P['n'] * this['d']) === C_ZERO;
		  },

		  /**
		   * Returns a decimal representation of the fraction
		   *
		   * Ex: new Fraction("100.'91823'").valueOf() => 100.91823918239183
		   **/
		  'valueOf': function () {
		    //if (this['n'] <= MAX_INTEGER && this['d'] <= MAX_INTEGER) {
		    return Number(this['s'] * this['n']) / Number(this['d']);
		    //}
		  },

		  /**
		   * Creates a string representation of a fraction with all digits
		   *
		   * Ex: new Fraction("100.'91823'").toString() => "100.(91823)"
		   **/
		  'toString': function (dec = 15) {

		    let N = this["n"];
		    let D = this["d"];

		    let cycLen = cycleLen(N, D); // Cycle length
		    let cycOff = cycleStart(N, D, cycLen); // Cycle start

		    let str = this['s'] < C_ZERO ? "-" : "";

		    // Append integer part
		    str += ifloor(N / D);

		    N %= D;
		    N *= C_TEN;

		    if (N)
		      str += ".";

		    if (cycLen) {

		      for (let i = cycOff; i--;) {
		        str += ifloor(N / D);
		        N %= D;
		        N *= C_TEN;
		      }
		      str += "(";
		      for (let i = cycLen; i--;) {
		        str += ifloor(N / D);
		        N %= D;
		        N *= C_TEN;
		      }
		      str += ")";
		    } else {
		      for (let i = dec; N && i--;) {
		        str += ifloor(N / D);
		        N %= D;
		        N *= C_TEN;
		      }
		    }
		    return str;
		  },

		  /**
		   * Returns a string-fraction representation of a Fraction object
		   *
		   * Ex: new Fraction("1.'3'").toFraction() => "4 1/3"
		   **/
		  'toFraction': function (showMixed = false) {

		    let n = this["n"];
		    let d = this["d"];
		    let str = this['s'] < C_ZERO ? "-" : "";

		    if (d === C_ONE) {
		      str += n;
		    } else {
		      const whole = ifloor(n / d);
		      if (showMixed && whole > C_ZERO) {
		        str += whole;
		        str += " ";
		        n %= d;
		      }

		      str += n;
		      str += '/';
		      str += d;
		    }
		    return str;
		  },

		  /**
		   * Returns a latex representation of a Fraction object
		   *
		   * Ex: new Fraction("1.'3'").toLatex() => "\frac{4}{3}"
		   **/
		  'toLatex': function (showMixed = false) {

		    let n = this["n"];
		    let d = this["d"];
		    let str = this['s'] < C_ZERO ? "-" : "";

		    if (d === C_ONE) {
		      str += n;
		    } else {
		      const whole = ifloor(n / d);
		      if (showMixed && whole > C_ZERO) {
		        str += whole;
		        n %= d;
		      }

		      str += "\\frac{";
		      str += n;
		      str += '}{';
		      str += d;
		      str += '}';
		    }
		    return str;
		  },

		  /**
		   * Returns an array of continued fraction elements
		   *
		   * Ex: new Fraction("7/8").toContinued() => [0,1,7]
		   */
		  'toContinued': function () {

		    let a = this['n'];
		    let b = this['d'];
		    const res = [];

		    while (b) {
		      res.push(ifloor(a / b));
		      const t = a % b;
		      a = b;
		      b = t;
		    }
		    return res;
		  },

		  "simplify": function (eps = 1e-3) {

		    // Continued fractions give best approximations for a max denominator,
		    // generally outperforming mediants in denominator–accuracy trade-offs.
		    // Semiconvergents can further reduce the denominator within tolerance.

		    const ieps = BigInt(Math.ceil(1 / eps));

		    const thisABS = this['abs']();
		    const cont = thisABS['toContinued']();

		    for (let i = 1; i < cont.length; i++) {

		      let s = newFraction(cont[i - 1], C_ONE);
		      for (let k = i - 2; k >= 0; k--) {
		        s = s['inverse']()['add'](cont[k]);
		      }

		      let t = s['sub'](thisABS);
		      if (t['n'] * ieps < t['d']) { // More robust than Math.abs(t.valueOf()) < eps
		        return s['mul'](this['s']);
		      }
		    }
		    return this;
		  }
		};

		Object.defineProperty(Fraction, "__esModule", { 'value': true });
		Fraction['default'] = Fraction;
		Fraction['Fraction'] = Fraction;
		module['exports'] = Fraction; 
	} (fraction$2));
	return fraction$2.exports;
}

var hasRequiredFraction$2;

function requireFraction$2 () {
	if (hasRequiredFraction$2) return Fraction;
	hasRequiredFraction$2 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(Fraction, "__esModule", {
	  value: true
	});
	Fraction.createFractionClass = void 0;
	var _fraction = _interopRequireDefault(/*@__PURE__*/ requireFraction$3());
	var _factory = requireFactory$b();
	const name = 'Fraction';
	const dependencies = [];
	Fraction.createFractionClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, () => {
	  /**
	   * Attach type information
	   */
	  Object.defineProperty(_fraction.default, 'name', {
	    value: 'Fraction'
	  });
	  _fraction.default.prototype.constructor = _fraction.default;
	  _fraction.default.prototype.type = 'Fraction';
	  _fraction.default.prototype.isFraction = true;

	  /**
	   * Get a JSON representation of a Fraction containing type information
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "Fraction", "n": "3", "d": "8"}`
	   */
	  _fraction.default.prototype.toJSON = function () {
	    return {
	      mathjs: 'Fraction',
	      n: String(this.s * this.n),
	      d: String(this.d)
	    };
	  };

	  /**
	   * Instantiate a Fraction from a JSON object
	   * @param {Object} json  a JSON object structured as:
	   *                       `{"mathjs": "Fraction", "n": "3", "d": "8"}`
	   * @return {BigNumber}
	   */
	  _fraction.default.fromJSON = function (json) {
	    return new _fraction.default(json);
	  };
	  return _fraction.default;
	}, {
	  isClass: true
	});
	return Fraction;
}

var Range = {};

var hasRequiredRange$2;

function requireRange$2 () {
	if (hasRequiredRange$2) return Range;
	hasRequiredRange$2 = 1;

	Object.defineProperty(Range, "__esModule", {
	  value: true
	});
	Range.createRangeClass = void 0;
	var _is = requireIs();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	const name = 'Range';
	const dependencies = [];
	Range.createRangeClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, () => {
	  /**
	   * Create a range of numbers. A range has a start, step, and end,
	   * and contains functions to iterate over the range.
	   *
	   * A range can be constructed as:
	   *
	   *     const range = new Range(start, end)
	   *     const range = new Range(start, end, step)
	   *
	   * Note that the endpoints and step may be specified with other numeric
	   * types such as bigint or BigNumber, but they will be demoted to the
	   * built-in `number` type and the Range will only contain numbers. The
	   * rationale for this demotion is that Range objects are primarily used
	   * for indexing Matrix objects, and Matrix objects may only be indexed
	   * with `number`s.
	   *
	   * To get the result of the range:
	   *     range.forEach(function (x) {
	   *         console.log(x)
	   *     })
	   *     range.map(function (x) {
	   *         return math.sin(x)
	   *     })
	   *     range.toArray()
	   *
	   * Example usage:
	   *
	   *     const c = new Range(2, 6)       // 2:1:5
	   *     c.toArray()                     // [2, 3, 4, 5]
	   *     const d = new Range(2, -3, -1)  // 2:-1:-2
	   *     d.toArray()                     // [2, 1, 0, -1, -2]
	   *
	   * @class Range
	   * @constructor Range
	   * @param {number} start  included lower bound
	   * @param {number} end    excluded upper bound
	   * @param {number} [step] step size, default value is 1
	   */
	  function Range(start, end, step) {
	    if (!(this instanceof Range)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    const hasStart = start !== null && start !== undefined;
	    const hasEnd = end !== null && end !== undefined;
	    const hasStep = step !== null && step !== undefined;
	    if (hasStart) {
	      if ((0, _is.isBigNumber)(start)) {
	        start = start.toNumber();
	      } else if (typeof start !== 'number' && !(0, _is.isBigInt)(start)) {
	        throw new TypeError('Parameter start must be a number or bigint');
	      }
	    }
	    if (hasEnd) {
	      if ((0, _is.isBigNumber)(end)) {
	        end = end.toNumber();
	      } else if (typeof end !== 'number' && !(0, _is.isBigInt)(end)) {
	        throw new TypeError('Parameter end must be a number or bigint');
	      }
	    }
	    if (hasStep) {
	      if ((0, _is.isBigNumber)(step)) {
	        step = step.toNumber();
	      } else if (typeof step !== 'number' && !(0, _is.isBigInt)(step)) {
	        throw new TypeError('Parameter step must be a number or bigint');
	      }
	    }
	    this.start = hasStart ? parseFloat(start) : 0;
	    this.end = hasEnd ? parseFloat(end) : 0;
	    this.step = hasStep ? parseFloat(step) : 1;
	    if (hasStep && (0, _number.nearlyEqual)(this.step, 0)) {
	      throw new Error('Step must not be zero');
	    }
	  }

	  /**
	   * Attach type information
	   */
	  Range.prototype.type = 'Range';
	  Range.prototype.isRange = true;

	  /**
	   * Parse a string into a range,
	   * The string contains the start, optional step, and end, separated by a colon.
	   * If the string does not contain a valid range, null is returned.
	   * For example str='0:2:11'.
	   * @memberof Range
	   * @param {string} str
	   * @return {Range | null} range
	   */
	  Range.parse = function (str) {
	    if (typeof str !== 'string') {
	      return null;
	    }
	    const args = str.split(':');
	    const nums = args.map(function (arg) {
	      return parseFloat(arg);
	    });
	    const invalid = nums.some(function (num) {
	      return isNaN(num);
	    });
	    if (invalid) {
	      return null;
	    }
	    switch (nums.length) {
	      case 2:
	        return new Range(nums[0], nums[1]);
	      case 3:
	        return new Range(nums[0], nums[2], nums[1]);
	      default:
	        return null;
	    }
	  };

	  /**
	   * Create a clone of the range
	   * @return {Range} clone
	   */
	  Range.prototype.clone = function () {
	    return new Range(this.start, this.end, this.step);
	  };

	  /**
	   * Retrieve the size of the range.
	   * Returns an array containing one number, the number of elements in the range.
	   * @memberof Range
	   * @returns {number[]} size
	   */
	  Range.prototype.size = function () {
	    let len = 0;
	    const start = this.start;
	    const step = this.step;
	    const end = this.end;
	    const diff = end - start;
	    if ((0, _number.sign)(step) === (0, _number.sign)(diff)) {
	      len = Math.ceil(diff / step);
	    } else if (diff === 0) {
	      len = 0;
	    }
	    if (isNaN(len)) {
	      len = 0;
	    }
	    return [len];
	  };

	  /**
	   * Calculate the minimum value in the range
	   * @memberof Range
	   * @return {number | undefined} min
	   */
	  Range.prototype.min = function () {
	    const size = this.size()[0];
	    if (size > 0) {
	      if (this.step > 0) {
	        // positive step
	        return this.start;
	      } else {
	        // negative step
	        return this.start + (size - 1) * this.step;
	      }
	    } else {
	      return undefined;
	    }
	  };

	  /**
	   * Calculate the maximum value in the range
	   * @memberof Range
	   * @return {number | undefined} max
	   */
	  Range.prototype.max = function () {
	    const size = this.size()[0];
	    if (size > 0) {
	      if (this.step > 0) {
	        // positive step
	        return this.start + (size - 1) * this.step;
	      } else {
	        // negative step
	        return this.start;
	      }
	    } else {
	      return undefined;
	    }
	  };

	  /**
	   * Execute a callback function for each value in the range.
	   * @memberof Range
	   * @param {function} callback   The callback method is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Range being traversed.
	   */
	  Range.prototype.forEach = function (callback) {
	    let x = this.start;
	    const step = this.step;
	    const end = this.end;
	    let i = 0;
	    if (step > 0) {
	      while (x < end) {
	        callback(x, [i], this);
	        x += step;
	        i++;
	      }
	    } else if (step < 0) {
	      while (x > end) {
	        callback(x, [i], this);
	        x += step;
	        i++;
	      }
	    }
	  };

	  /**
	   * Execute a callback function for each value in the Range, and return the
	   * results as an array
	   * @memberof Range
	   * @param {function} callback   The callback method is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   * @returns {Array} array
	   */
	  Range.prototype.map = function (callback) {
	    const array = [];
	    this.forEach(function (value, index, obj) {
	      array[index[0]] = callback(value, index, obj);
	    });
	    return array;
	  };

	  /**
	   * Create an Array with a copy of the Ranges data
	   * @memberof Range
	   * @returns {Array} array
	   */
	  Range.prototype.toArray = function () {
	    const array = [];
	    this.forEach(function (value, index) {
	      array[index[0]] = value;
	    });
	    return array;
	  };

	  /**
	   * Get the primitive value of the Range, a one dimensional array
	   * @memberof Range
	   * @returns {Array} array
	   */
	  Range.prototype.valueOf = function () {
	    // TODO: implement a caching mechanism for range.valueOf()
	    return this.toArray();
	  };

	  /**
	   * Get a string representation of the range, with optional formatting options.
	   * Output is formatted as 'start:step:end', for example '2:6' or '0:0.2:11'
	   * @memberof Range
	   * @param {Object | number | function} [options]  Formatting options. See
	   *                                                lib/utils/number:format for a
	   *                                                description of the available
	   *                                                options.
	   * @returns {string} str
	   */
	  Range.prototype.format = function (options) {
	    let str = (0, _number.format)(this.start, options);
	    if (this.step !== 1) {
	      str += ':' + (0, _number.format)(this.step, options);
	    }
	    str += ':' + (0, _number.format)(this.end, options);
	    return str;
	  };

	  /**
	   * Get a string representation of the range.
	   * @memberof Range
	   * @returns {string}
	   */
	  Range.prototype.toString = function () {
	    return this.format();
	  };

	  /**
	   * Get a JSON representation of the range
	   * @memberof Range
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "Range", "start": 2, "end": 4, "step": 1}`
	   */
	  Range.prototype.toJSON = function () {
	    return {
	      mathjs: 'Range',
	      start: this.start,
	      end: this.end,
	      step: this.step
	    };
	  };

	  /**
	   * Instantiate a Range from a JSON object
	   * @memberof Range
	   * @param {Object} json A JSON object structured as:
	   *                      `{"mathjs": "Range", "start": 2, "end": 4, "step": 1}`
	   * @return {Range}
	   */
	  Range.fromJSON = function (json) {
	    return new Range(json.start, json.end, json.step);
	  };
	  return Range;
	}, {
	  isClass: true
	});
	return Range;
}

var Matrix$1 = {};

var hasRequiredMatrix$2;

function requireMatrix$2 () {
	if (hasRequiredMatrix$2) return Matrix$1;
	hasRequiredMatrix$2 = 1;

	Object.defineProperty(Matrix$1, "__esModule", {
	  value: true
	});
	Matrix$1.createMatrixClass = void 0;
	var _factory = requireFactory$b();
	const name = 'Matrix';
	const dependencies = [];
	Matrix$1.createMatrixClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, () => {
	  /**
	   * @constructor Matrix
	   *
	   * A Matrix is a wrapper around an Array. A matrix can hold a multi dimensional
	   * array. A matrix can be constructed as:
	   *
	   *     let matrix = math.matrix(data)
	   *
	   * Matrix contains the functions to resize, get and set values, get the size,
	   * clone the matrix and to convert the matrix to a vector, array, or scalar.
	   * Furthermore, one can iterate over the matrix using map and forEach.
	   * The internal Array of the Matrix can be accessed using the function valueOf.
	   *
	   * Example usage:
	   *
	   *     let matrix = math.matrix([[1, 2], [3, 4]])
	   *     matix.size()              // [2, 2]
	   *     matrix.resize([3, 2], 5)
	   *     matrix.valueOf()          // [[1, 2], [3, 4], [5, 5]]
	   *     matrix.subset([1,2])       // 3 (indexes are zero-based)
	   *
	   */
	  function Matrix() {
	    if (!(this instanceof Matrix)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	  }

	  /**
	   * Attach type information
	   */
	  Matrix.prototype.type = 'Matrix';
	  Matrix.prototype.isMatrix = true;

	  /**
	   * Get the storage format used by the matrix.
	   *
	   * Usage:
	   *     const format = matrix.storage()   // retrieve storage format
	   *
	   * @return {string}           The storage format.
	   */
	  Matrix.prototype.storage = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke storage on a Matrix interface');
	  };

	  /**
	   * Get the datatype of the data stored in the matrix.
	   *
	   * Usage:
	   *     const format = matrix.datatype()    // retrieve matrix datatype
	   *
	   * @return {string}           The datatype.
	   */
	  Matrix.prototype.datatype = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke datatype on a Matrix interface');
	  };

	  /**
	   * Create a new Matrix With the type of the current matrix instance
	   * @param {Array | Object} data
	   * @param {string} [datatype]
	   */
	  Matrix.prototype.create = function (data, datatype) {
	    throw new Error('Cannot invoke create on a Matrix interface');
	  };

	  /**
	   * Get a subset of the matrix, or replace a subset of the matrix.
	   *
	   * Usage:
	   *     const subset = matrix.subset(index)               // retrieve subset
	   *     const value = matrix.subset(index, replacement)   // replace subset
	   *
	   * @param {Index} index
	   * @param {Array | Matrix | *} [replacement]
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be filled with zeros.
	   */
	  Matrix.prototype.subset = function (index, replacement, defaultValue) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke subset on a Matrix interface');
	  };

	  /**
	   * Get a single element from the matrix.
	   * @param {number[]} index   Zero-based index
	   * @return {*} value
	   */
	  Matrix.prototype.get = function (index) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke get on a Matrix interface');
	  };

	  /**
	   * Replace a single element in the matrix.
	   * @param {number[]} index   Zero-based index
	   * @param {*} value
	   * @param {*} [defaultValue]        Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be left undefined.
	   * @return {Matrix} self
	   */
	  Matrix.prototype.set = function (index, value, defaultValue) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke set on a Matrix interface');
	  };

	  /**
	   * Resize the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (resize in place).
	   *
	   * @param {number[]} size           The new size the matrix should have.
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries.
	   *                                  If not provided, the matrix elements will
	   *                                  be filled with zeros.
	   * @param {boolean} [copy]          Return a resized copy of the matrix
	   *
	   * @return {Matrix}                 The resized matrix
	   */
	  Matrix.prototype.resize = function (size, defaultValue) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke resize on a Matrix interface');
	  };

	  /**
	   * Reshape the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (reshape in place).
	   *
	   * @param {number[]} size           The new size the matrix should have.
	   * @param {boolean} [copy]          Return a reshaped copy of the matrix
	   *
	   * @return {Matrix}                 The reshaped matrix
	   */
	  Matrix.prototype.reshape = function (size, defaultValue) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke reshape on a Matrix interface');
	  };

	  /**
	   * Create a clone of the matrix
	   * @return {Matrix} clone
	   */
	  Matrix.prototype.clone = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke clone on a Matrix interface');
	  };

	  /**
	   * Retrieve the size of the matrix.
	   * @returns {number[]} size
	   */
	  Matrix.prototype.size = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke size on a Matrix interface');
	  };

	  /**
	   * Create a new matrix with the results of the callback function executed on
	   * each entry of the matrix.
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   * @param {boolean} [skipZeros] Invoke callback function for non-zero values only.
	   *
	   * @return {Matrix} matrix
	   */
	  Matrix.prototype.map = function (callback, skipZeros) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke map on a Matrix interface');
	  };

	  /**
	   * Execute a callback function on each entry of the matrix.
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   */
	  Matrix.prototype.forEach = function (callback) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke forEach on a Matrix interface');
	  };

	  /**
	   * Iterate over the matrix elements
	   * @return {Iterable<{ value, index: number[] }>}
	   */
	  Matrix.prototype[Symbol.iterator] = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot iterate a Matrix interface');
	  };

	  /**
	   * Create an Array with a copy of the data of the Matrix
	   * @returns {Array} array
	   */
	  Matrix.prototype.toArray = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke toArray on a Matrix interface');
	  };

	  /**
	   * Get the primitive value of the Matrix: a multidimensional array
	   * @returns {Array} array
	   */
	  Matrix.prototype.valueOf = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke valueOf on a Matrix interface');
	  };

	  /**
	   * Get a string representation of the matrix, with optional formatting options.
	   * @param {Object | number | Function} [options]  Formatting options. See
	   *                                                lib/utils/number:format for a
	   *                                                description of the available
	   *                                                options.
	   * @returns {string} str
	   */
	  Matrix.prototype.format = function (options) {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke format on a Matrix interface');
	  };

	  /**
	   * Get a string representation of the matrix
	   * @returns {string} str
	   */
	  Matrix.prototype.toString = function () {
	    // must be implemented by each of the Matrix implementations
	    throw new Error('Cannot invoke toString on a Matrix interface');
	  };
	  return Matrix;
	}, {
	  isClass: true
	});
	return Matrix$1;
}

var DenseMatrix = {};

var array = {};

var string$2 = {};

var formatter = {};

var hasRequiredFormatter;

function requireFormatter () {
	if (hasRequiredFormatter) return formatter;
	hasRequiredFormatter = 1;

	Object.defineProperty(formatter, "__esModule", {
	  value: true
	});
	formatter.format = format;
	formatter.toEngineering = toEngineering;
	formatter.toExponential = toExponential;
	formatter.toFixed = toFixed;
	var _is = requireIs();
	var _number = requireNumber$3();
	/**
	 * Formats a BigNumber in a given base
	 * @param {BigNumber} n
	 * @param {number} base
	 * @param {number} size
	 * @returns {string}
	 */
	function formatBigNumberToBase(n, base, size) {
	  const BigNumberCtor = n.constructor;
	  const big2 = new BigNumberCtor(2);
	  let suffix = '';
	  if (size) {
	    if (size < 1) {
	      throw new Error('size must be in greater than 0');
	    }
	    if (!(0, _number.isInteger)(size)) {
	      throw new Error('size must be an integer');
	    }
	    if (n.greaterThan(big2.pow(size - 1).sub(1)) || n.lessThan(big2.pow(size - 1).mul(-1))) {
	      throw new Error(`Value must be in range [-2^${size - 1}, 2^${size - 1}-1]`);
	    }
	    if (!n.isInteger()) {
	      throw new Error('Value must be an integer');
	    }
	    if (n.lessThan(0)) {
	      n = n.add(big2.pow(size));
	    }
	    suffix = `i${size}`;
	  }
	  switch (base) {
	    case 2:
	      return `${n.toBinary()}${suffix}`;
	    case 8:
	      return `${n.toOctal()}${suffix}`;
	    case 16:
	      return `${n.toHexadecimal()}${suffix}`;
	    default:
	      throw new Error(`Base ${base} not supported `);
	  }
	}

	/**
	 * Convert a BigNumber to a formatted string representation.
	 *
	 * Syntax:
	 *
	 *    format(value)
	 *    format(value, options)
	 *    format(value, precision)
	 *    format(value, fn)
	 *
	 * Where:
	 *
	 *    {number} value   The value to be formatted
	 *    {Object} options An object with formatting options. Available options:
	 *                     {string} notation
	 *                         Number notation. Choose from:
	 *                         'fixed'          Always use regular number notation.
	 *                                          For example '123.40' and '14000000'
	 *                         'exponential'    Always use exponential notation.
	 *                                          For example '1.234e+2' and '1.4e+7'
	 *                         'auto' (default) Regular number notation for numbers
	 *                                          having an absolute value between
	 *                                          `lower` and `upper` bounds, and uses
	 *                                          exponential notation elsewhere.
	 *                                          Lower bound is included, upper bound
	 *                                          is excluded.
	 *                                          For example '123.4' and '1.4e7'.
	 *                         'bin', 'oct, or
	 *                         'hex'            Format the number using binary, octal,
	 *                                          or hexadecimal notation.
	 *                                          For example '0b1101' and '0x10fe'.
	 *                     {number} wordSize    The word size in bits to use for formatting
	 *                                          in binary, octal, or hexadecimal notation.
	 *                                          To be used only with 'bin', 'oct', or 'hex'
	 *                                          values for 'notation' option. When this option
	 *                                          is defined the value is formatted as a signed
	 *                                          twos complement integer of the given word size
	 *                                          and the size suffix is appended to the output.
	 *                                          For example
	 *                                          format(-1, {notation: 'hex', wordSize: 8}) === '0xffi8'.
	 *                                          Default value is undefined.
	 *                     {number} precision   A number between 0 and 16 to round
	 *                                          the digits of the number.
	 *                                          In case of notations 'exponential',
	 *                                          'engineering', and 'auto',
	 *                                          `precision` defines the total
	 *                                          number of significant digits returned.
	 *                                          In case of notation 'fixed',
	 *                                          `precision` defines the number of
	 *                                          significant digits after the decimal
	 *                                          point.
	 *                                          `precision` is undefined by default.
	 *                     {number} lowerExp    Exponent determining the lower boundary
	 *                                          for formatting a value with an exponent
	 *                                          when `notation='auto`.
	 *                                          Default value is `-3`.
	 *                     {number} upperExp    Exponent determining the upper boundary
	 *                                          for formatting a value with an exponent
	 *                                          when `notation='auto`.
	 *                                          Default value is `5`.
	 *    {Function} fn    A custom formatting function. Can be used to override the
	 *                     built-in notations. Function `fn` is called with `value` as
	 *                     parameter and must return a string. Is useful for example to
	 *                     format all values inside a matrix in a particular way.
	 *
	 * Examples:
	 *
	 *    format(6.4)                                        // '6.4'
	 *    format(1240000)                                    // '1.24e6'
	 *    format(1/3)                                        // '0.3333333333333333'
	 *    format(1/3, 3)                                     // '0.333'
	 *    format(21385, 2)                                   // '21000'
	 *    format(12e8, {notation: 'fixed'})                  // returns '1200000000'
	 *    format(2.3,    {notation: 'fixed', precision: 4})  // returns '2.3000'
	 *    format(52.8,   {notation: 'exponential'})          // returns '5.28e+1'
	 *    format(12400,  {notation: 'engineering'})          // returns '12.400e+3'
	 *
	 * @param {BigNumber} value
	 * @param {Object | Function | number | BigNumber} [options]
	 * @return {string} str The formatted value
	 */
	function format(value, options) {
	  if (typeof options === 'function') {
	    // handle format(value, fn)
	    return options(value);
	  }

	  // handle special cases
	  if (!value.isFinite()) {
	    return value.isNaN() ? 'NaN' : value.gt(0) ? 'Infinity' : '-Infinity';
	  }
	  const {
	    notation,
	    precision,
	    wordSize
	  } = (0, _number.normalizeFormatOptions)(options);

	  // handle the various notations
	  switch (notation) {
	    case 'fixed':
	      return toFixed(value, precision);
	    case 'exponential':
	      return toExponential(value, precision);
	    case 'engineering':
	      return toEngineering(value, precision);
	    case 'bin':
	      return formatBigNumberToBase(value, 2, wordSize);
	    case 'oct':
	      return formatBigNumberToBase(value, 8, wordSize);
	    case 'hex':
	      return formatBigNumberToBase(value, 16, wordSize);
	    case 'auto':
	      {
	        // determine lower and upper bound for exponential notation.
	        // TODO: implement support for upper and lower to be BigNumbers themselves
	        const lowerExp = _toNumberOrDefault(options === null || options === void 0 ? void 0 : options.lowerExp, -3);
	        const upperExp = _toNumberOrDefault(options === null || options === void 0 ? void 0 : options.upperExp, 5);

	        // handle special case zero
	        if (value.isZero()) return '0';

	        // determine whether or not to output exponential notation
	        let str;
	        const rounded = value.toSignificantDigits(precision);
	        const exp = rounded.e;
	        if (exp >= lowerExp && exp < upperExp) {
	          // normal number notation
	          str = rounded.toFixed();
	        } else {
	          // exponential notation
	          str = toExponential(value, precision);
	        }

	        // remove trailing zeros after the decimal point
	        return str.replace(/((\.\d*?)(0+))($|e)/, function () {
	          const digits = arguments[2];
	          const e = arguments[4];
	          return digits !== '.' ? digits + e : e;
	        });
	      }
	    default:
	      throw new Error('Unknown notation "' + notation + '". ' + 'Choose "auto", "exponential", "fixed", "bin", "oct", or "hex.');
	  }
	}

	/**
	 * Format a BigNumber in engineering notation. Like '1.23e+6', '2.3e+0', '3.500e-3'
	 * @param {BigNumber} value
	 * @param {number} [precision]        Optional number of significant figures to return.
	 */
	function toEngineering(value, precision) {
	  // find nearest lower multiple of 3 for exponent
	  const e = value.e;
	  const newExp = e % 3 === 0 ? e : e < 0 ? e - 3 - e % 3 : e - e % 3;

	  // find difference in exponents, and calculate the value without exponent
	  const valueWithoutExp = value.mul(Math.pow(10, -newExp));
	  let valueStr = valueWithoutExp.toPrecision(precision);
	  if (valueStr.includes('e')) {
	    const BigNumber = value.constructor;
	    valueStr = new BigNumber(valueStr).toFixed();
	  }
	  return valueStr + 'e' + (e >= 0 ? '+' : '') + newExp.toString();
	}

	/**
	 * Format a number in exponential notation. Like '1.23e+5', '2.3e+0', '3.500e-3'
	 * @param {BigNumber} value
	 * @param {number} [precision]  Number of digits in formatted output.
	 *                              If not provided, the maximum available digits
	 *                              is used.
	 * @returns {string} str
	 */
	function toExponential(value, precision) {
	  if (precision !== undefined) {
	    return value.toExponential(precision - 1); // Note the offset of one
	  } else {
	    return value.toExponential();
	  }
	}

	/**
	 * Format a number with fixed notation.
	 * @param {BigNumber} value
	 * @param {number} [precision=undefined] Optional number of decimals after the
	 *                                       decimal point. Undefined by default.
	 */
	function toFixed(value, precision) {
	  return value.toFixed(precision);
	}
	function _toNumberOrDefault(value, defaultValue) {
	  if ((0, _is.isNumber)(value)) {
	    return value;
	  } else if ((0, _is.isBigNumber)(value)) {
	    return value.toNumber();
	  } else {
	    return defaultValue;
	  }
	}
	return formatter;
}

var hasRequiredString$2;

function requireString$2 () {
	if (hasRequiredString$2) return string$2;
	hasRequiredString$2 = 1;

	Object.defineProperty(string$2, "__esModule", {
	  value: true
	});
	string$2.compareText = compareText;
	string$2.endsWith = endsWith;
	string$2.escape = escape;
	string$2.format = format;
	string$2.stringify = stringify;
	var _is = requireIs();
	var _number = requireNumber$3();
	var _formatter = requireFormatter();
	/**
	 * Check if a text ends with a certain string.
	 * @param {string} text
	 * @param {string} search
	 */
	function endsWith(text, search) {
	  const start = text.length - search.length;
	  const end = text.length;
	  return text.substring(start, end) === search;
	}

	/**
	 * Format a value of any type into a string.
	 *
	 * Usage:
	 *     math.format(value)
	 *     math.format(value, precision)
	 *     math.format(value, options)
	 *
	 * When value is a function:
	 *
	 * - When the function has a property `syntax`, it returns this
	 *   syntax description.
	 * - In other cases, a string `'function'` is returned.
	 *
	 * When `value` is an Object:
	 *
	 * - When the object contains a property `format` being a function, this
	 *   function is invoked as `value.format(options)` and the result is returned.
	 * - When the object has its own `toString` method, this method is invoked
	 *   and the result is returned.
	 * - In other cases the function will loop over all object properties and
	 *   return JSON object notation like '{"a": 2, "b": 3}'.
	 *
	 * Example usage:
	 *     math.format(2/7)                // '0.2857142857142857'
	 *     math.format(math.pi, 3)         // '3.14'
	 *     math.format(new Complex(2, 3))  // '2 + 3i'
	 *     math.format('hello')            // '"hello"'
	 *
	 * @param {*} value             Value to be stringified
	 * @param {Object | number | Function} [options]
	 *     Formatting options. See src/utils/number.js:format for a
	 *     description of the available options controlling number output.
	 *     This generic "format" also supports the option property `truncate: NN`
	 *     giving the maximum number NN of characters to return (if there would
	 *     have been more, they are deleted and replaced by an ellipsis).
	 * @return {string} str
	 */
	function format(value, options) {
	  const result = _format(value, options);
	  if (options && typeof options === 'object' && 'truncate' in options && result.length > options.truncate) {
	    return result.substring(0, options.truncate - 3) + '...';
	  }
	  return result;
	}
	function _format(value, options) {
	  if (typeof value === 'number') {
	    return (0, _number.format)(value, options);
	  }
	  if ((0, _is.isBigNumber)(value)) {
	    return (0, _formatter.format)(value, options);
	  }

	  // note: we use unsafe duck-typing here to check for Fractions, this is
	  // ok here since we're only invoking toString or concatenating its values
	  if (looksLikeFraction(value)) {
	    if (!options || options.fraction !== 'decimal') {
	      // output as ratio, like '1/3'
	      return `${value.s * value.n}/${value.d}`;
	    } else {
	      // output as decimal, like '0.(3)'
	      return value.toString();
	    }
	  }
	  if (Array.isArray(value)) {
	    return formatArray(value, options);
	  }
	  if ((0, _is.isString)(value)) {
	    return stringify(value);
	  }
	  if (typeof value === 'function') {
	    return value.syntax ? String(value.syntax) : 'function';
	  }
	  if (value && typeof value === 'object') {
	    if (typeof value.format === 'function') {
	      return value.format(options);
	    } else if (value && value.toString(options) !== {}.toString()) {
	      // this object has a non-native toString method, use that one
	      return value.toString(options);
	    } else {
	      const entries = Object.keys(value).map(key => {
	        return stringify(key) + ': ' + format(value[key], options);
	      });
	      return '{' + entries.join(', ') + '}';
	    }
	  }
	  return String(value);
	}

	/**
	 * Stringify a value into a string enclosed in double quotes.
	 * Unescaped double quotes and backslashes inside the value are escaped.
	 * @param {*} value
	 * @return {string}
	 */
	function stringify(value) {
	  const text = String(value);
	  let escaped = '';
	  let i = 0;
	  while (i < text.length) {
	    const c = text.charAt(i);
	    escaped += c in controlCharacters ? controlCharacters[c] : c;
	    i++;
	  }
	  return '"' + escaped + '"';
	}
	const controlCharacters = {
	  '"': '\\"',
	  '\\': '\\\\',
	  '\b': '\\b',
	  '\f': '\\f',
	  '\n': '\\n',
	  '\r': '\\r',
	  '\t': '\\t'
	};

	/**
	 * Escape special HTML characters
	 * @param {*} value
	 * @return {string}
	 */
	function escape(value) {
	  let text = String(value);
	  text = text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	  return text;
	}

	/**
	 * Recursively format an n-dimensional matrix
	 * Example output: "[[1, 2], [3, 4]]"
	 * @param {Array} array
	 * @param {Object | number | Function} [options]  Formatting options. See
	 *                                                lib/utils/number:format for a
	 *                                                description of the available
	 *                                                options.
	 * @returns {string} str
	 */
	function formatArray(array, options) {
	  if (Array.isArray(array)) {
	    let str = '[';
	    const len = array.length;
	    for (let i = 0; i < len; i++) {
	      if (i !== 0) {
	        str += ', ';
	      }
	      str += formatArray(array[i], options);
	    }
	    str += ']';
	    return str;
	  } else {
	    return format(array, options);
	  }
	}

	/**
	 * Check whether a value looks like a Fraction (unsafe duck-type check)
	 * @param {*} value
	 * @return {boolean}
	 */
	function looksLikeFraction(value) {
	  return value && typeof value === 'object' && typeof value.s === 'bigint' && typeof value.n === 'bigint' && typeof value.d === 'bigint' || false;
	}

	/**
	 * Compare two strings
	 * @param {string} x
	 * @param {string} y
	 * @returns {number}
	 */
	function compareText(x, y) {
	  // we don't want to convert numbers to string, only accept string input
	  if (!(0, _is.isString)(x)) {
	    throw new TypeError('Unexpected type of argument in function compareText ' + '(expected: string or Array or Matrix, actual: ' + (0, _is.typeOf)(x) + ', index: 0)');
	  }
	  if (!(0, _is.isString)(y)) {
	    throw new TypeError('Unexpected type of argument in function compareText ' + '(expected: string or Array or Matrix, actual: ' + (0, _is.typeOf)(y) + ', index: 1)');
	  }
	  return x === y ? 0 : x > y ? 1 : -1;
	}
	return string$2;
}

var DimensionError = {};

var hasRequiredDimensionError;

function requireDimensionError () {
	if (hasRequiredDimensionError) return DimensionError;
	hasRequiredDimensionError = 1;

	Object.defineProperty(DimensionError, "__esModule", {
	  value: true
	});
	DimensionError.DimensionError = DimensionError$1;
	/**
	 * Create a range error with the message:
	 *     'Dimension mismatch (<actual size> != <expected size>)'
	 * @param {number | number[]} actual        The actual size
	 * @param {number | number[]} expected      The expected size
	 * @param {string} [relation='!=']          Optional relation between actual
	 *                                          and expected size: '!=', '<', etc.
	 * @extends RangeError
	 */
	function DimensionError$1(actual, expected, relation) {
	  if (!(this instanceof DimensionError$1)) {
	    throw new SyntaxError('Constructor must be called with the new operator');
	  }
	  this.actual = actual;
	  this.expected = expected;
	  this.relation = relation;
	  this.message = 'Dimension mismatch (' + (Array.isArray(actual) ? '[' + actual.join(', ') + ']' : actual) + ' ' + (this.relation || '!=') + ' ' + (Array.isArray(expected) ? '[' + expected.join(', ') + ']' : expected) + ')';
	  this.stack = new Error().stack;
	}
	DimensionError$1.prototype = new RangeError();
	DimensionError$1.prototype.constructor = RangeError;
	DimensionError$1.prototype.name = 'DimensionError';
	DimensionError$1.prototype.isDimensionError = true;
	return DimensionError;
}

var IndexError = {};

var hasRequiredIndexError;

function requireIndexError () {
	if (hasRequiredIndexError) return IndexError;
	hasRequiredIndexError = 1;

	Object.defineProperty(IndexError, "__esModule", {
	  value: true
	});
	IndexError.IndexError = IndexError$1;
	/**
	 * Create a range error with the message:
	 *     'Index out of range (index < min)'
	 *     'Index out of range (index < max)'
	 *
	 * @param {number} index     The actual index
	 * @param {number} [min=0]   Minimum index (included)
	 * @param {number} [max]     Maximum index (excluded)
	 * @extends RangeError
	 */
	function IndexError$1(index, min, max) {
	  if (!(this instanceof IndexError$1)) {
	    throw new SyntaxError('Constructor must be called with the new operator');
	  }
	  this.index = index;
	  if (arguments.length < 3) {
	    this.min = 0;
	    this.max = min;
	  } else {
	    this.min = min;
	    this.max = max;
	  }
	  if (this.min !== undefined && this.index < this.min) {
	    this.message = 'Index out of range (' + this.index + ' < ' + this.min + ')';
	  } else if (this.max !== undefined && this.index >= this.max) {
	    this.message = 'Index out of range (' + this.index + ' > ' + (this.max - 1) + ')';
	  } else {
	    this.message = 'Index out of range (' + this.index + ')';
	  }
	  this.stack = new Error().stack;
	}
	IndexError$1.prototype = new RangeError();
	IndexError$1.prototype.constructor = RangeError;
	IndexError$1.prototype.name = 'IndexError';
	IndexError$1.prototype.isIndexError = true;
	return IndexError;
}

var hasRequiredArray;

function requireArray () {
	if (hasRequiredArray) return array;
	hasRequiredArray = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(array, "__esModule", {
	  value: true
	});
	array.arraySize = arraySize;
	array.broadcastArrays = broadcastArrays;
	array.broadcastSizes = broadcastSizes;
	array.broadcastTo = broadcastTo;
	array.checkBroadcastingRules = checkBroadcastingRules;
	array.clone = clone;
	array.concat = concat;
	array.deepForEach = deepForEach;
	array.deepMap = deepMap;
	array.filter = filter;
	array.filterRegExp = filterRegExp;
	array.flatten = flatten;
	array.forEach = forEach;
	array.generalize = generalize;
	array.get = get;
	array.getArrayDataType = getArrayDataType;
	array.identify = identify;
	array.initial = initial;
	array.isEmptyIndex = isEmptyIndex;
	array.join = join;
	array.last = last;
	array.map = map;
	array.processSizesWildcard = processSizesWildcard;
	array.reshape = reshape;
	array.resize = resize;
	array.squeeze = squeeze;
	array.stretch = stretch;
	array.unsqueeze = unsqueeze;
	array.validate = validate;
	array.validateIndex = validateIndex;
	array.validateIndexSourceSize = validateIndexSourceSize;
	var _extends2 = _interopRequireDefault(require_extends());
	var _number = requireNumber$3();
	var _is = requireIs();
	var _string = requireString$2();
	var _DimensionError = requireDimensionError();
	var _IndexError = requireIndexError();
	var _object = requireObject$3();
	/**
	 * Calculate the size of a multi dimensional array.
	 * This function checks the size of the first entry, it does not validate
	 * whether all dimensions match. (use function `validate` for that)
	 * @param {Array} x
	 * @return {number[]} size
	 */
	function arraySize(x) {
	  const s = [];
	  while (Array.isArray(x)) {
	    s.push(x.length);
	    x = x[0];
	  }
	  return s;
	}

	/**
	 * Recursively validate whether each element in a multi dimensional array
	 * has a size corresponding to the provided size array.
	 * @param {Array} array    Array to be validated
	 * @param {number[]} size  Array with the size of each dimension
	 * @param {number} dim     Current dimension
	 * @throws DimensionError
	 * @private
	 */
	function _validate(array, size, dim) {
	  let i;
	  const len = array.length;
	  if (len !== size[dim]) {
	    throw new _DimensionError.DimensionError(len, size[dim]);
	  }
	  if (dim < size.length - 1) {
	    // recursively validate each child array
	    const dimNext = dim + 1;
	    for (i = 0; i < len; i++) {
	      const child = array[i];
	      if (!Array.isArray(child)) {
	        throw new _DimensionError.DimensionError(size.length - 1, size.length, '<');
	      }
	      _validate(array[i], size, dimNext);
	    }
	  } else {
	    // last dimension. none of the children may be an array
	    for (i = 0; i < len; i++) {
	      if (Array.isArray(array[i])) {
	        throw new _DimensionError.DimensionError(size.length + 1, size.length, '>');
	      }
	    }
	  }
	}

	/**
	 * Validate whether each element in a multi dimensional array has
	 * a size corresponding to the provided size array.
	 * @param {Array} array    Array to be validated
	 * @param {number[]} size  Array with the size of each dimension
	 * @throws DimensionError
	 */
	function validate(array, size) {
	  const isScalar = size.length === 0;
	  if (isScalar) {
	    // scalar
	    if (Array.isArray(array)) {
	      throw new _DimensionError.DimensionError(array.length, 0);
	    }
	  } else {
	    // array
	    _validate(array, size, 0);
	  }
	}

	/**
	 * Validate whether the source of the index matches the size of the Array
	 * @param {Array | Matrix} value    Array to be validated
	 * @param {Index} index  Index with the source information to validate
	 * @throws DimensionError
	 */
	function validateIndexSourceSize(value, index) {
	  const valueSize = value.isMatrix ? value._size : arraySize(value);
	  const sourceSize = index._sourceSize;
	  // checks if the source size is not null and matches the valueSize
	  sourceSize.forEach((sourceDim, i) => {
	    if (sourceDim !== null && sourceDim !== valueSize[i]) {
	      throw new _DimensionError.DimensionError(sourceDim, valueSize[i]);
	    }
	  });
	}

	/**
	 * Test whether index is an integer number with index >= 0 and index < length
	 * when length is provided
	 * @param {number} index    Zero-based index
	 * @param {number} [length] Length of the array
	 */
	function validateIndex(index, length) {
	  if (index !== undefined) {
	    if (!(0, _is.isNumber)(index) || !(0, _number.isInteger)(index)) {
	      throw new TypeError('Index must be an integer (value: ' + index + ')');
	    }
	    if (index < 0 || typeof length === 'number' && index >= length) {
	      throw new _IndexError.IndexError(index, length);
	    }
	  }
	}

	/**
	 * Test if an index has empty values
	 * @param {Index} index    Zero-based index
	 */
	function isEmptyIndex(index) {
	  for (let i = 0; i < index._dimensions.length; ++i) {
	    const dimension = index._dimensions[i];
	    if (dimension._data && (0, _is.isArray)(dimension._data)) {
	      if (dimension._size[0] === 0) {
	        return true;
	      }
	    } else if (dimension.isRange) {
	      if (dimension.start === dimension.end) {
	        return true;
	      }
	    } else if ((0, _is.isString)(dimension)) {
	      if (dimension.length === 0) {
	        return true;
	      }
	    }
	  }
	  return false;
	}

	/**
	 * Resize a multi dimensional array. The resized array is returned.
	 * @param {Array | number} array         Array to be resized
	 * @param {number[]} size Array with the size of each dimension
	 * @param {*} [defaultValue=0]  Value to be filled in new entries,
	 *                              zero by default. Specify for example `null`,
	 *                              to clearly see entries that are not explicitly
	 *                              set.
	 * @return {Array} array         The resized array
	 */
	function resize(array, size, defaultValue) {
	  // check the type of the arguments
	  if (!Array.isArray(size)) {
	    throw new TypeError('Array expected');
	  }
	  if (size.length === 0) {
	    throw new Error('Resizing to scalar is not supported');
	  }

	  // check whether size contains positive integers
	  size.forEach(function (value) {
	    if (!(0, _is.isNumber)(value) || !(0, _number.isInteger)(value) || value < 0) {
	      throw new TypeError('Invalid size, must contain positive integers ' + '(size: ' + (0, _string.format)(size) + ')');
	    }
	  });

	  // convert number to an array
	  if ((0, _is.isNumber)(array) || (0, _is.isBigNumber)(array)) {
	    array = [array];
	  }

	  // recursively resize the array
	  const _defaultValue = defaultValue !== undefined ? defaultValue : 0;
	  _resize(array, size, 0, _defaultValue);
	  return array;
	}

	/**
	 * Recursively resize a multi dimensional array
	 * @param {Array} array         Array to be resized
	 * @param {number[]} size       Array with the size of each dimension
	 * @param {number} dim          Current dimension
	 * @param {*} [defaultValue]    Value to be filled in new entries,
	 *                              undefined by default.
	 * @private
	 */
	function _resize(array, size, dim, defaultValue) {
	  let i;
	  let elem;
	  const oldLen = array.length;
	  const newLen = size[dim];
	  const minLen = Math.min(oldLen, newLen);

	  // apply new length
	  array.length = newLen;
	  if (dim < size.length - 1) {
	    // non-last dimension
	    const dimNext = dim + 1;

	    // resize existing child arrays
	    for (i = 0; i < minLen; i++) {
	      // resize child array
	      elem = array[i];
	      if (!Array.isArray(elem)) {
	        elem = [elem]; // add a dimension
	        array[i] = elem;
	      }
	      _resize(elem, size, dimNext, defaultValue);
	    }

	    // create new child arrays
	    for (i = minLen; i < newLen; i++) {
	      // get child array
	      elem = [];
	      array[i] = elem;

	      // resize new child array
	      _resize(elem, size, dimNext, defaultValue);
	    }
	  } else {
	    // last dimension

	    // remove dimensions of existing values
	    for (i = 0; i < minLen; i++) {
	      while (Array.isArray(array[i])) {
	        array[i] = array[i][0];
	      }
	    }

	    // fill new elements with the default value
	    for (i = minLen; i < newLen; i++) {
	      array[i] = defaultValue;
	    }
	  }
	}

	/**
	 * Re-shape a multi dimensional array to fit the specified dimensions
	 * @param {Array} array           Array to be reshaped
	 * @param {number[]} sizes        List of sizes for each dimension
	 * @returns {Array}               Array whose data has been formatted to fit the
	 *                                specified dimensions
	 *
	 * @throws {DimensionError}       If the product of the new dimension sizes does
	 *                                not equal that of the old ones
	 */
	function reshape(array, sizes) {
	  const flatArray = flatten(array, true); // since it has rectangular
	  const currentLength = flatArray.length;
	  if (!Array.isArray(array) || !Array.isArray(sizes)) {
	    throw new TypeError('Array expected');
	  }
	  if (sizes.length === 0) {
	    throw new _DimensionError.DimensionError(0, currentLength, '!=');
	  }
	  sizes = processSizesWildcard(sizes, currentLength);
	  const newLength = product(sizes);
	  if (currentLength !== newLength) {
	    throw new _DimensionError.DimensionError(newLength, currentLength, '!=');
	  }
	  try {
	    return _reshape(flatArray, sizes);
	  } catch (e) {
	    if (e instanceof _DimensionError.DimensionError) {
	      throw new _DimensionError.DimensionError(newLength, currentLength, '!=');
	    }
	    throw e;
	  }
	}

	/**
	 * Replaces the wildcard -1 in the sizes array.
	 * @param {number[]} sizes  List of sizes for each dimension. At most one wildcard.
	 * @param {number} currentLength  Number of elements in the array.
	 * @throws {Error}                If more than one wildcard or unable to replace it.
	 * @returns {number[]}      The sizes array with wildcard replaced.
	 */
	function processSizesWildcard(sizes, currentLength) {
	  const newLength = product(sizes);
	  const processedSizes = sizes.slice();
	  const WILDCARD = -1;
	  const wildCardIndex = sizes.indexOf(WILDCARD);
	  const isMoreThanOneWildcard = sizes.indexOf(WILDCARD, wildCardIndex + 1) >= 0;
	  if (isMoreThanOneWildcard) {
	    throw new Error('More than one wildcard in sizes');
	  }
	  const hasWildcard = wildCardIndex >= 0;
	  const canReplaceWildcard = currentLength % newLength === 0;
	  if (hasWildcard) {
	    if (canReplaceWildcard) {
	      processedSizes[wildCardIndex] = -currentLength / newLength;
	    } else {
	      throw new Error('Could not replace wildcard, since ' + currentLength + ' is no multiple of ' + -newLength);
	    }
	  }
	  return processedSizes;
	}

	/**
	 * Computes the product of all array elements.
	 * @param {number[]} array Array of factors
	 * @returns {number}            Product of all elements
	 */
	function product(array) {
	  return array.reduce((prev, curr) => prev * curr, 1);
	}

	/**
	 * Iteratively re-shape a multi dimensional array to fit the specified dimensions
	 * @param {Array} array           Array to be reshaped
	 * @param {number[]} sizes  List of sizes for each dimension
	 * @returns {Array}               Array whose data has been formatted to fit the
	 *                                specified dimensions
	 */

	function _reshape(array, sizes) {
	  // testing if there are enough elements for the requested shape
	  let tmpArray = array;
	  let tmpArray2;
	  // for each dimension starting by the last one and ignoring the first one
	  for (let sizeIndex = sizes.length - 1; sizeIndex > 0; sizeIndex--) {
	    const size = sizes[sizeIndex];
	    tmpArray2 = [];

	    // aggregate the elements of the current tmpArray in elements of the requested size
	    const length = tmpArray.length / size;
	    for (let i = 0; i < length; i++) {
	      tmpArray2.push(tmpArray.slice(i * size, (i + 1) * size));
	    }
	    // set it as the new tmpArray for the next loop turn or for return
	    tmpArray = tmpArray2;
	  }
	  return tmpArray;
	}

	/**
	 * Squeeze a multi dimensional array
	 * @param {Array} array
	 * @param {Array} [size]
	 * @returns {Array} returns the array itself
	 */
	function squeeze(array, size) {
	  const s = size || arraySize(array);

	  // squeeze outer dimensions
	  while (Array.isArray(array) && array.length === 1) {
	    array = array[0];
	    s.shift();
	  }

	  // find the first dimension to be squeezed
	  let dims = s.length;
	  while (s[dims - 1] === 1) {
	    dims--;
	  }

	  // squeeze inner dimensions
	  if (dims < s.length) {
	    array = _squeeze(array, dims, 0);
	    s.length = dims;
	  }
	  return array;
	}

	/**
	 * Recursively squeeze a multi dimensional array
	 * @param {Array} array
	 * @param {number} dims Required number of dimensions
	 * @param {number} dim  Current dimension
	 * @returns {Array | *} Returns the squeezed array
	 * @private
	 */
	function _squeeze(array, dims, dim) {
	  let i, ii;
	  if (dim < dims) {
	    const next = dim + 1;
	    for (i = 0, ii = array.length; i < ii; i++) {
	      array[i] = _squeeze(array[i], dims, next);
	    }
	  } else {
	    while (Array.isArray(array)) {
	      array = array[0];
	    }
	  }
	  return array;
	}

	/**
	 * Unsqueeze a multi dimensional array: add dimensions when missing
	 *
	 * Parameter `size` will be mutated to match the new, unsqueezed matrix size.
	 *
	 * @param {Array} array
	 * @param {number} dims       Desired number of dimensions of the array
	 * @param {number} [outer]    Number of outer dimensions to be added
	 * @param {Array} [size] Current size of array.
	 * @returns {Array} returns the array itself
	 * @private
	 */
	function unsqueeze(array, dims, outer, size) {
	  const s = size || arraySize(array);

	  // unsqueeze outer dimensions
	  if (outer) {
	    for (let i = 0; i < outer; i++) {
	      array = [array];
	      s.unshift(1);
	    }
	  }

	  // unsqueeze inner dimensions
	  array = _unsqueeze(array, dims, 0);
	  while (s.length < dims) {
	    s.push(1);
	  }
	  return array;
	}

	/**
	 * Recursively unsqueeze a multi dimensional array
	 * @param {Array} array
	 * @param {number} dims Required number of dimensions
	 * @param {number} dim  Current dimension
	 * @returns {Array | *} Returns the unsqueezed array
	 * @private
	 */
	function _unsqueeze(array, dims, dim) {
	  let i, ii;
	  if (Array.isArray(array)) {
	    const next = dim + 1;
	    for (i = 0, ii = array.length; i < ii; i++) {
	      array[i] = _unsqueeze(array[i], dims, next);
	    }
	  } else {
	    for (let d = dim; d < dims; d++) {
	      array = [array];
	    }
	  }
	  return array;
	}
	/**
	 * Flatten a multi dimensional array, put all elements in a one dimensional
	 * array
	 * @param {Array} array   A multi dimensional array
	 * @param {boolean} isRectangular Optional. If the array is rectangular (not jagged)
	 * @return {Array}        The flattened array (1 dimensional)
	 */
	function flatten(array) {
	  let isRectangular = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
	  if (!Array.isArray(array)) {
	    // if not an array, return as is
	    return array;
	  }
	  if (typeof isRectangular !== 'boolean') {
	    throw new TypeError('Boolean expected for second argument of flatten');
	  }
	  const flat = [];
	  if (isRectangular) {
	    _flattenRectangular(array);
	  } else {
	    _flatten(array);
	  }
	  return flat;
	  function _flatten(array) {
	    for (let i = 0; i < array.length; i++) {
	      const item = array[i];
	      if (Array.isArray(item)) {
	        _flatten(item);
	      } else {
	        flat.push(item);
	      }
	    }
	  }
	  function _flattenRectangular(array) {
	    if (Array.isArray(array[0])) {
	      for (let i = 0; i < array.length; i++) {
	        _flattenRectangular(array[i]);
	      }
	    } else {
	      for (let i = 0; i < array.length; i++) {
	        flat.push(array[i]);
	      }
	    }
	  }
	}

	/**
	 * A safe map
	 * @param {Array} array
	 * @param {function} callback
	 */
	function map(array, callback) {
	  return Array.prototype.map.call(array, callback);
	}

	/**
	 * A safe forEach
	 * @param {Array} array
	 * @param {function} callback
	 */
	function forEach(array, callback) {
	  Array.prototype.forEach.call(array, callback);
	}

	/**
	 * A safe filter
	 * @param {Array} array
	 * @param {function} callback
	 */
	function filter(array, callback) {
	  if (arraySize(array).length !== 1) {
	    throw new Error('Only one dimensional matrices supported');
	  }
	  return Array.prototype.filter.call(array, callback);
	}

	/**
	 * Filter values in an array given a regular expression
	 * @param {Array} array
	 * @param {RegExp} regexp
	 * @return {Array} Returns the filtered array
	 * @private
	 */
	function filterRegExp(array, regexp) {
	  if (arraySize(array).length !== 1) {
	    throw new Error('Only one dimensional matrices supported');
	  }
	  return Array.prototype.filter.call(array, entry => regexp.test(entry));
	}

	/**
	 * A safe join
	 * @param {Array} array
	 * @param {string} separator
	 */
	function join(array, separator) {
	  return Array.prototype.join.call(array, separator);
	}

	/**
	 * Assign a numeric identifier to every element of a sorted array
	 * @param {Array} a  An array
	 * @return {Array} An array of objects containing the original value and its identifier
	 */
	function identify(a) {
	  if (!Array.isArray(a)) {
	    throw new TypeError('Array input expected');
	  }
	  if (a.length === 0) {
	    return a;
	  }
	  const b = [];
	  let count = 0;
	  b[0] = {
	    value: a[0],
	    identifier: 0
	  };
	  for (let i = 1; i < a.length; i++) {
	    if (a[i] === a[i - 1]) {
	      count++;
	    } else {
	      count = 0;
	    }
	    b.push({
	      value: a[i],
	      identifier: count
	    });
	  }
	  return b;
	}

	/**
	 * Remove the numeric identifier from the elements
	 * @param {array} a  An array
	 * @return {array} An array of values without identifiers
	 */
	function generalize(a) {
	  if (!Array.isArray(a)) {
	    throw new TypeError('Array input expected');
	  }
	  if (a.length === 0) {
	    return a;
	  }
	  const b = [];
	  for (let i = 0; i < a.length; i++) {
	    b.push(a[i].value);
	  }
	  return b;
	}

	/**
	 * Check the datatype of a given object
	 * This is a low level implementation that should only be used by
	 * parent Matrix classes such as SparseMatrix or DenseMatrix
	 * This method does not validate Array Matrix shape
	 * @param {Array} array
	 * @param {function} typeOf   Callback function to use to determine the type of a value
	 * @return {string}
	 */
	function getArrayDataType(array, typeOf) {
	  let type; // to hold type info
	  let length = 0; // to hold length value to ensure it has consistent sizes

	  for (let i = 0; i < array.length; i++) {
	    const item = array[i];
	    const isArray = Array.isArray(item);

	    // Saving the target matrix row size
	    if (i === 0 && isArray) {
	      length = item.length;
	    }

	    // If the current item is an array but the length does not equal the targetVectorSize
	    if (isArray && item.length !== length) {
	      return undefined;
	    }
	    const itemType = isArray ? getArrayDataType(item, typeOf) // recurse into a nested array
	    : typeOf(item);
	    if (type === undefined) {
	      type = itemType; // first item
	    } else if (type !== itemType) {
	      return 'mixed';
	    } else ;
	  }
	  return type;
	}

	/**
	 * Return the last item from an array
	 * @param {Array} array
	 * @returns {*}
	 */
	function last(array) {
	  return array[array.length - 1];
	}

	/**
	 * Get all but the last element of array.
	 * @param {Array} array
	 * @returns {Array}
	 */
	function initial(array) {
	  return array.slice(0, array.length - 1);
	}

	/**
	 * Recursively concatenate two matrices.
	 * The contents of the matrices are not cloned.
	 * @param {Array} a             Multi dimensional array
	 * @param {Array} b             Multi dimensional array
	 * @param {number} concatDim    The dimension on which to concatenate (zero-based)
	 * @param {number} dim          The current dim (zero-based)
	 * @return {Array} c            The concatenated matrix
	 * @private
	 */
	function concatRecursive(a, b, concatDim, dim) {
	  if (dim < concatDim) {
	    // recurse into next dimension
	    if (a.length !== b.length) {
	      throw new _DimensionError.DimensionError(a.length, b.length);
	    }
	    const c = [];
	    for (let i = 0; i < a.length; i++) {
	      c[i] = concatRecursive(a[i], b[i], concatDim, dim + 1);
	    }
	    return c;
	  } else {
	    // concatenate this dimension
	    return a.concat(b);
	  }
	}

	/**
	 * Concatenates many arrays in the specified direction
	 * @param {...Array} arrays All the arrays to concatenate
	 * @param {number} concatDim The dimension on which to concatenate (zero-based)
	 * @returns {Array}
	 */
	function concat() {
	  const arrays = Array.prototype.slice.call(arguments, 0, -1);
	  const concatDim = Array.prototype.slice.call(arguments, -1);
	  if (arrays.length === 1) {
	    return arrays[0];
	  }
	  if (arrays.length > 1) {
	    return arrays.slice(1).reduce(function (A, B) {
	      return concatRecursive(A, B, concatDim, 0);
	    }, arrays[0]);
	  } else {
	    throw new Error('Wrong number of arguments in function concat');
	  }
	}

	/**
	 * Receives two or more sizes and gets the broadcasted size for both.
	 * @param  {...number[]} sizes Sizes to broadcast together
	 * @returns {number[]} The broadcasted size
	 */
	function broadcastSizes() {
	  for (var _len = arguments.length, sizes = new Array(_len), _key = 0; _key < _len; _key++) {
	    sizes[_key] = arguments[_key];
	  }
	  const dimensions = sizes.map(s => s.length);
	  const N = Math.max(...dimensions);
	  const sizeMax = new Array(N).fill(null);
	  // check for every size
	  for (let i = 0; i < sizes.length; i++) {
	    const size = sizes[i];
	    const dim = dimensions[i];
	    for (let j = 0; j < dim; j++) {
	      const n = N - dim + j;
	      if (size[j] > sizeMax[n]) {
	        sizeMax[n] = size[j];
	      }
	    }
	  }
	  for (let i = 0; i < sizes.length; i++) {
	    checkBroadcastingRules(sizes[i], sizeMax);
	  }
	  return sizeMax;
	}

	/**
	 * Checks if it's possible to broadcast a size to another size
	 * @param {number[]} size The size of the array to check
	 * @param {number[]} toSize The size of the array to validate if it can be broadcasted to
	 */
	function checkBroadcastingRules(size, toSize) {
	  const N = toSize.length;
	  const dim = size.length;
	  for (let j = 0; j < dim; j++) {
	    const n = N - dim + j;
	    if (size[j] < toSize[n] && size[j] > 1 || size[j] > toSize[n]) {
	      throw new Error(`shape mismatch: mismatch is found in arg with shape (${size}) not possible to broadcast dimension ${dim} with size ${size[j]} to size ${toSize[n]}`);
	    }
	  }
	}

	/**
	 * Broadcasts a single array to a certain size
	 * @param {Array} array Array to be broadcasted
	 * @param {number[]} toSize Size to broadcast the array
	 * @returns {Array} The broadcasted array
	 */
	function broadcastTo(array, toSize) {
	  let Asize = arraySize(array);
	  if ((0, _object.deepStrictEqual)(Asize, toSize)) {
	    return array;
	  }
	  checkBroadcastingRules(Asize, toSize);
	  const broadcastedSize = broadcastSizes(Asize, toSize);
	  const N = broadcastedSize.length;
	  const paddedSize = [...Array(N - Asize.length).fill(1), ...Asize];
	  let A = clone(array);
	  // reshape A if needed to make it ready for concat
	  if (Asize.length < N) {
	    A = reshape(A, paddedSize);
	    Asize = arraySize(A);
	  }

	  // stretches the array on each dimension to make it the same size as index
	  for (let dim = 0; dim < N; dim++) {
	    if (Asize[dim] < broadcastedSize[dim]) {
	      A = stretch(A, broadcastedSize[dim], dim);
	      Asize = arraySize(A);
	    }
	  }
	  return A;
	}

	/**
	 * Broadcasts arrays and returns the broadcasted arrays in an array
	 * @param  {...Array | any} arrays
	 * @returns {Array[]} The broadcasted arrays
	 */
	function broadcastArrays() {
	  for (var _len2 = arguments.length, arrays = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	    arrays[_key2] = arguments[_key2];
	  }
	  if (arrays.length === 0) {
	    throw new Error('Insufficient number of arguments in function broadcastArrays');
	  }
	  if (arrays.length === 1) {
	    return arrays[0];
	  }
	  const sizes = arrays.map(function (array) {
	    return arraySize(array);
	  });
	  const broadcastedSize = broadcastSizes(...sizes);
	  const broadcastedArrays = [];
	  arrays.forEach(function (array) {
	    broadcastedArrays.push(broadcastTo(array, broadcastedSize));
	  });
	  return broadcastedArrays;
	}

	/**
	 * Stretches a matrix up to a certain size in a certain dimension
	 * @param {Array} arrayToStretch
	 * @param {number[]} sizeToStretch
	 * @param {number} dimToStretch
	 * @returns {Array} The stretched array
	 */
	function stretch(arrayToStretch, sizeToStretch, dimToStretch) {
	  return concat(...Array(sizeToStretch).fill(arrayToStretch), dimToStretch);
	}

	/**
	* Retrieves a single element from an array given an index.
	*
	* @param {Array} array - The array from which to retrieve the value.
	* @param {Array<number>} index - An array of indices specifying the position of the desired element in each dimension.
	* @returns {*} - The value at the specified position in the array.
	*
	* @example
	* const arr = [[[1, 2], [3, 4]], [[5, 6], [7, 8]]];
	* const index = [1, 0, 1];
	* console.log(get(arr, index)); // 6
	*/
	function get(array, index) {
	  if (!Array.isArray(array)) {
	    throw new Error('Array expected');
	  }
	  if (!Array.isArray(index)) {
	    throw new Error('Array expected for index');
	  }
	  const size = arraySize(array);
	  if (index.length !== size.length) {
	    throw new _DimensionError.DimensionError(index.length, size.length);
	  }
	  for (let x = 0; x < index.length; x++) {
	    validateIndex(index[x], size[x]);
	  }
	  return index.reduce((acc, curr) => acc[curr], array);
	}

	/**
	 * Recursively maps over each element of nested array using a provided callback function.
	 *
	 * @param {Array} array - The array to be mapped.
	 * @param {Function} callback - The function to execute on each element, taking three arguments:
	 *   - `value` (any): The current element being processed in the array.
	 *   - `index` (Array<number>): The index of the current element being processed in the array.
	 *   - `array` (Array): The array `deepMap` was called upon.
	 * @param {boolean} [skipIndex=false] - If true, the callback function is called with only the value.
	 * @returns {Array} A new array with each element being the result of the callback function.
	 */
	function deepMap(array, callback) {
	  let skipIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
	  if (array.length === 0) {
	    return [];
	  }
	  if (skipIndex) {
	    return recursiveMap(array);
	  }
	  const index = [];
	  return recursiveMapWithIndex(array, 0);
	  function recursiveMapWithIndex(value, depth) {
	    if (Array.isArray(value)) {
	      const N = value.length;
	      const result = Array(N);
	      for (let i = 0; i < N; i++) {
	        index[depth] = i;
	        result[i] = recursiveMapWithIndex(value[i], depth + 1);
	      }
	      return result;
	    } else {
	      return callback(value, index.slice(0, depth), array);
	    }
	  }
	  function recursiveMap(value) {
	    if (Array.isArray(value)) {
	      const N = value.length;
	      const result = Array(N);
	      for (let i = 0; i < N; i++) {
	        result[i] = recursiveMap(value[i]);
	      }
	      return result;
	    } else {
	      return callback(value);
	    }
	  }
	}

	/**
	 * Recursively iterates over each element in a multi-dimensional array and applies a callback function.
	 *
	 * @param {Array} array - The multi-dimensional array to iterate over.
	 * @param {Function} callback - The function to execute for each element. It receives three arguments:
	 *   - {any} value: The current element being processed in the array.
	 *   - {Array<number>} index: The index of the current element in each dimension.
	 *   - {Array} array: The original array being processed.
	 * @param {boolean} [skipIndex=false] - If true, the callback function is called with only the value.
	 */
	function deepForEach(array, callback) {
	  let skipIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
	  if (array.length === 0) {
	    return;
	  }
	  if (skipIndex) {
	    recursiveForEach(array);
	    return;
	  }
	  const index = [];
	  recursiveForEachWithIndex(array, 0);
	  function recursiveForEachWithIndex(value, depth) {
	    if (Array.isArray(value)) {
	      const N = value.length;
	      for (let i = 0; i < N; i++) {
	        index[depth] = i;
	        recursiveForEachWithIndex(value[i], depth + 1);
	      }
	    } else {
	      callback(value, index.slice(0, depth), array);
	    }
	  }
	  function recursiveForEach(value) {
	    if (Array.isArray(value)) {
	      const N = value.length;
	      for (let i = 0; i < N; i++) {
	        recursiveForEach(value[i]);
	      }
	    } else {
	      callback(value);
	    }
	  }
	}

	/**
	 * Deep clones a multidimensional array
	 * @param {Array} array
	 * @returns {Array} cloned array
	 */
	function clone(array) {
	  return (0, _extends2.default)([], array);
	}
	return array;
}

var optimizeCallback = {};

var hasRequiredOptimizeCallback;

function requireOptimizeCallback () {
	if (hasRequiredOptimizeCallback) return optimizeCallback;
	hasRequiredOptimizeCallback = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(optimizeCallback, "__esModule", {
	  value: true
	});
	optimizeCallback.optimizeCallback = optimizeCallback$1;
	var _typedFunction = _interopRequireDefault(require$$2$1);
	var _array = requireArray();
	var _is = requireIs();
	/**
	 * Simplifies a callback function by reducing its complexity and potentially improving its performance.
	 *
	 * @param {Function} callback The original callback function to simplify.
	 * @param {Array|Matrix} array The array that will be used with the callback function.
	 * @param {string} name The name of the function that is using the callback.
	 * @param {boolean} isUnary If true, the callback function is unary and will be optimized as such.
	 * @returns {Function} Returns a simplified version of the callback function.
	 */
	function optimizeCallback$1(callback, array, name, isUnary) {
	  if (_typedFunction.default.isTypedFunction(callback)) {
	    let numberOfArguments;
	    if (isUnary) {
	      numberOfArguments = 1;
	    } else {
	      const size = array.isMatrix ? array.size() : (0, _array.arraySize)(array);

	      // Check the size of the last dimension to see if the array/matrix is empty
	      const isEmpty = size.length ? size[size.length - 1] === 0 : true;
	      if (isEmpty) {
	        // don't optimize callbacks for empty arrays/matrix, as they will never be called
	        // and in fact will throw an exception when we try to access the first element below
	        return {
	          isUnary,
	          fn: callback
	        };
	      }
	      const firstIndex = size.map(() => 0);
	      const firstValue = array.isMatrix ? array.get(firstIndex) : (0, _array.get)(array, firstIndex);
	      numberOfArguments = _findNumberOfArgumentsTyped(callback, firstValue, firstIndex, array);
	    }
	    let fastCallback;
	    if (array.isMatrix && array.dataType !== 'mixed' && array.dataType !== undefined) {
	      const singleSignature = _findSingleSignatureWithArity(callback, numberOfArguments);
	      fastCallback = singleSignature !== undefined ? singleSignature : callback;
	    } else {
	      fastCallback = callback;
	    }
	    if (numberOfArguments >= 1 && numberOfArguments <= 3) {
	      return {
	        isUnary: numberOfArguments === 1,
	        fn: function () {
	          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
	            args[_key] = arguments[_key];
	          }
	          return _tryFunctionWithArgs(fastCallback, args.slice(0, numberOfArguments), name, callback.name);
	        }
	      };
	    }
	    return {
	      isUnary: false,
	      fn: function () {
	        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	          args[_key2] = arguments[_key2];
	        }
	        return _tryFunctionWithArgs(fastCallback, args, name, callback.name);
	      }
	    };
	  }
	  if (isUnary === undefined) {
	    return {
	      isUnary: _findIfCallbackIsUnary(callback),
	      fn: callback
	    };
	  } else {
	    return {
	      isUnary,
	      fn: callback
	    };
	  }
	}
	function _findSingleSignatureWithArity(callback, arity) {
	  const matchingFunctions = [];
	  Object.entries(callback.signatures).forEach(_ref => {
	    let [signature, func] = _ref;
	    if (signature.split(',').length === arity) {
	      matchingFunctions.push(func);
	    }
	  });
	  if (matchingFunctions.length === 1) {
	    return matchingFunctions[0];
	  }
	}

	/**
	 * Determines if a given callback function is unary (i.e., takes exactly one argument).
	 *
	 * This function checks the following conditions to determine if the callback is unary:
	 * 1. The callback function should have exactly one parameter.
	 * 2. The callback function should not use the `arguments` object.
	 * 3. The callback function should not use rest parameters (`...`).
	 * If in doubt, this function shall return `false` to be safe
	 *
	 * @param {Function} callback - The callback function to be checked.
	 * @returns {boolean} - Returns `true` if the callback is unary, otherwise `false`.
	 */
	function _findIfCallbackIsUnary(callback) {
	  if (callback.length !== 1) return false;
	  const callbackStr = callback.toString();
	  // Check if the callback function uses `arguments`
	  if (/arguments/.test(callbackStr)) return false;

	  // Extract the parameters of the callback function
	  const paramsStr = callbackStr.match(/\(.*?\)/);
	  // Check if the callback function uses rest parameters
	  if (/\.\.\./.test(paramsStr)) return false;
	  return true;
	}
	function _findNumberOfArgumentsTyped(callback, value, index, array) {
	  const testArgs = [value, index, array];
	  for (let i = 3; i > 0; i--) {
	    const args = testArgs.slice(0, i);
	    if (_typedFunction.default.resolve(callback, args) !== null) {
	      return i;
	    }
	  }
	}

	/**
	   * @param {function} func The selected function taken from one of the signatures of the callback function
	   * @param {Array} args List with arguments to apply to the selected signature
	   * @param {string} mappingFnName the name of the function that is using the callback
	   * @param {string} callbackName the name of the callback function
	   * @returns {*} Returns the return value of the invoked signature
	   * @throws {TypeError} Throws an error when no matching signature was found
	   */
	function _tryFunctionWithArgs(func, args, mappingFnName, callbackName) {
	  try {
	    return func(...args);
	  } catch (err) {
	    _createCallbackError(err, args, mappingFnName, callbackName);
	  }
	}

	/**
	 * Creates and throws a detailed TypeError when a callback function fails.
	 *
	 * @param {Error} err The original error thrown by the callback function.
	 * @param {Array} args The arguments that were passed to the callback function.
	 * @param {string} mappingFnName The name of the function that is using the callback.
	 * @param {string} callbackName The name of the callback function.
	 * @throws {TypeError} Throws a detailed TypeError with enriched error message.
	 */
	function _createCallbackError(err, args, mappingFnName, callbackName) {
	  var _err$data;
	  // Enrich the error message so the user understands that it took place inside the callback function
	  if (err instanceof TypeError && ((_err$data = err.data) === null || _err$data === void 0 ? void 0 : _err$data.category) === 'wrongType') {
	    const argsDesc = [];
	    argsDesc.push(`value: ${(0, _is.typeOf)(args[0])}`);
	    if (args.length >= 2) {
	      argsDesc.push(`index: ${(0, _is.typeOf)(args[1])}`);
	    }
	    if (args.length >= 3) {
	      argsDesc.push(`array: ${(0, _is.typeOf)(args[2])}`);
	    }
	    throw new TypeError(`Function ${mappingFnName} cannot apply callback arguments ` + `${callbackName}(${argsDesc.join(', ')}) at index ${JSON.stringify(args[1])}`);
	  } else {
	    throw new TypeError(`Function ${mappingFnName} cannot apply callback arguments ` + `to function ${callbackName}: ${err.message}`);
	  }
	}
	return optimizeCallback;
}

var hasRequiredDenseMatrix;

function requireDenseMatrix () {
	if (hasRequiredDenseMatrix) return DenseMatrix;
	hasRequiredDenseMatrix = 1;

	Object.defineProperty(DenseMatrix, "__esModule", {
	  value: true
	});
	DenseMatrix.createDenseMatrixClass = void 0;
	var _is = requireIs();
	var _array = requireArray();
	var _string = requireString$2();
	var _number = requireNumber$3();
	var _object = requireObject$3();
	var _DimensionError = requireDimensionError();
	var _factory = requireFactory$b();
	var _optimizeCallback = requireOptimizeCallback();
	// deno-lint-ignore-file no-this-alias

	const name = 'DenseMatrix';
	const dependencies = ['Matrix', 'config'];
	DenseMatrix.createDenseMatrixClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Matrix,
	    config
	  } = _ref;
	  /**
	   * Dense Matrix implementation. A regular, dense matrix, supporting multi-dimensional matrices. This is the default matrix type.
	   * @class DenseMatrix
	   * @enum {{ value, index: number[] }}
	   */
	  function DenseMatrix(data, datatype) {
	    if (!(this instanceof DenseMatrix)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    if (datatype && !(0, _is.isString)(datatype)) {
	      throw new Error('Invalid datatype: ' + datatype);
	    }
	    if ((0, _is.isMatrix)(data)) {
	      // check data is a DenseMatrix
	      if (data.type === 'DenseMatrix') {
	        // clone data & size
	        this._data = (0, _object.clone)(data._data);
	        this._size = (0, _object.clone)(data._size);
	        this._datatype = datatype || data._datatype;
	      } else {
	        // build data from existing matrix
	        this._data = data.toArray();
	        this._size = data.size();
	        this._datatype = datatype || data._datatype;
	      }
	    } else if (data && (0, _is.isArray)(data.data) && (0, _is.isArray)(data.size)) {
	      // initialize fields from JSON representation
	      this._data = data.data;
	      this._size = data.size;
	      // verify the dimensions of the array
	      (0, _array.validate)(this._data, this._size);
	      this._datatype = datatype || data.datatype;
	    } else if ((0, _is.isArray)(data)) {
	      // replace nested Matrices with Arrays
	      this._data = preprocess(data);
	      // get the dimensions of the array
	      this._size = (0, _array.arraySize)(this._data);
	      // verify the dimensions of the array, TODO: compute size while processing array
	      (0, _array.validate)(this._data, this._size);
	      // data type unknown
	      this._datatype = datatype;
	    } else if (data) {
	      // unsupported type
	      throw new TypeError('Unsupported type of data (' + (0, _is.typeOf)(data) + ')');
	    } else {
	      // nothing provided
	      this._data = [];
	      this._size = [0];
	      this._datatype = datatype;
	    }
	  }
	  DenseMatrix.prototype = new Matrix();

	  /**
	   * Create a new DenseMatrix
	   */
	  DenseMatrix.prototype.createDenseMatrix = function (data, datatype) {
	    return new DenseMatrix(data, datatype);
	  };

	  /**
	   * Attach type information
	   */
	  Object.defineProperty(DenseMatrix, 'name', {
	    value: 'DenseMatrix'
	  });
	  DenseMatrix.prototype.constructor = DenseMatrix;
	  DenseMatrix.prototype.type = 'DenseMatrix';
	  DenseMatrix.prototype.isDenseMatrix = true;

	  /**
	   * Get the matrix type
	   *
	   * Usage:
	   *    const matrixType = matrix.getDataType()  // retrieves the matrix type
	   *
	   * @memberOf DenseMatrix
	   * @return {string}   type information; if multiple types are found from the Matrix, it will return "mixed"
	   */
	  DenseMatrix.prototype.getDataType = function () {
	    return (0, _array.getArrayDataType)(this._data, _is.typeOf);
	  };

	  /**
	   * Get the storage format used by the matrix.
	   *
	   * Usage:
	   *     const format = matrix.storage()  // retrieve storage format
	   *
	   * @memberof DenseMatrix
	   * @return {string}           The storage format.
	   */
	  DenseMatrix.prototype.storage = function () {
	    return 'dense';
	  };

	  /**
	   * Get the datatype of the data stored in the matrix.
	   *
	   * Usage:
	   *     const format = matrix.datatype()   // retrieve matrix datatype
	   *
	   * @memberof DenseMatrix
	   * @return {string}           The datatype.
	   */
	  DenseMatrix.prototype.datatype = function () {
	    return this._datatype;
	  };

	  /**
	   * Create a new DenseMatrix
	   * @memberof DenseMatrix
	   * @param {Array} data
	   * @param {string} [datatype]
	   */
	  DenseMatrix.prototype.create = function (data, datatype) {
	    return new DenseMatrix(data, datatype);
	  };

	  /**
	   * Get a subset of the matrix, or replace a subset of the matrix.
	   *
	   * Usage:
	   *     const subset = matrix.subset(index)               // retrieve subset
	   *     const value = matrix.subset(index, replacement)   // replace subset
	   *
	   * @memberof DenseMatrix
	   * @param {Index} index
	   * @param {Array | Matrix | *} [replacement]
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be filled with zeros.
	   */
	  DenseMatrix.prototype.subset = function (index, replacement, defaultValue) {
	    switch (arguments.length) {
	      case 1:
	        return _get(this, index);

	      // intentional fall through
	      case 2:
	      case 3:
	        return _set(this, index, replacement, defaultValue);
	      default:
	        throw new SyntaxError('Wrong number of arguments');
	    }
	  };

	  /**
	   * Get a single element from the matrix.
	   * @memberof DenseMatrix
	   * @param {number[]} index   Zero-based index
	   * @return {*} value
	   */
	  DenseMatrix.prototype.get = function (index) {
	    return (0, _array.get)(this._data, index);
	  };

	  /**
	   * Replace a single element in the matrix.
	   * @memberof DenseMatrix
	   * @param {number[]} index   Zero-based index
	   * @param {*} value
	   * @param {*} [defaultValue]        Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be left undefined.
	   * @return {DenseMatrix} self
	   */
	  DenseMatrix.prototype.set = function (index, value, defaultValue) {
	    if (!(0, _is.isArray)(index)) {
	      throw new TypeError('Array expected');
	    }
	    if (index.length < this._size.length) {
	      throw new _DimensionError.DimensionError(index.length, this._size.length, '<');
	    }
	    let i, ii, indexI;

	    // enlarge matrix when needed
	    const size = index.map(function (i) {
	      return i + 1;
	    });
	    _fit(this, size, defaultValue);

	    // traverse over the dimensions
	    let data = this._data;
	    for (i = 0, ii = index.length - 1; i < ii; i++) {
	      indexI = index[i];
	      (0, _array.validateIndex)(indexI, data.length);
	      data = data[indexI];
	    }

	    // set new value
	    indexI = index[index.length - 1];
	    (0, _array.validateIndex)(indexI, data.length);
	    data[indexI] = value;
	    return this;
	  };

	  /**
	   * Get a submatrix of this matrix
	   * @memberof DenseMatrix
	   * @param {DenseMatrix} matrix
	   * @param {Index} index   Zero-based index
	   * @private
	   */
	  function _get(matrix, index) {
	    if (!(0, _is.isIndex)(index)) {
	      throw new TypeError('Invalid index');
	    }
	    const isScalar = config.legacySubset ? index.size().every(idx => idx === 1) : index.isScalar();
	    if (isScalar) {
	      // return a scalar
	      return matrix.get(index.min());
	    } else {
	      // validate dimensions
	      const size = index.size();
	      if (size.length !== matrix._size.length) {
	        throw new _DimensionError.DimensionError(size.length, matrix._size.length);
	      }

	      // validate if any of the ranges in the index is out of range
	      const min = index.min();
	      const max = index.max();
	      for (let i = 0, ii = matrix._size.length; i < ii; i++) {
	        (0, _array.validateIndex)(min[i], matrix._size[i]);
	        (0, _array.validateIndex)(max[i], matrix._size[i]);
	      }

	      // retrieve submatrix
	      const returnMatrix = new DenseMatrix();
	      const submatrix = _getSubmatrix(matrix._data, index);
	      returnMatrix._size = submatrix.size;
	      returnMatrix._datatype = matrix._datatype;
	      returnMatrix._data = submatrix.data;
	      return config.legacySubset ? returnMatrix.reshape(index.size()) : returnMatrix;
	    }
	  }

	  /**
	   * Get a submatrix of a multi dimensional matrix.
	   * Index is not checked for correct number or length of dimensions.
	   * @memberof DenseMatrix
	   * @param {Array} data
	   * @param {Index} index
	   * @return {Array} submatrix
	   * @private
	   */
	  function _getSubmatrix(data, index) {
	    const maxDepth = index.size().length - 1;
	    const size = Array(maxDepth);
	    return {
	      data: getSubmatrixRecursive(data),
	      size: size.filter(x => x !== null)
	    };
	    function getSubmatrixRecursive(data) {
	      let depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	      const dims = index.dimension(depth);
	      function _mapIndex(dim, callback) {
	        // applies a callback for when the index is a Number or a Matrix
	        if ((0, _is.isNumber)(dim)) return callback(dim);else return dim.map(callback).valueOf();
	      }
	      if ((0, _is.isNumber)(dims)) {
	        size[depth] = null;
	      } else {
	        size[depth] = dims.size()[0];
	      }
	      if (depth < maxDepth) {
	        return _mapIndex(dims, dimIndex => {
	          (0, _array.validateIndex)(dimIndex, data.length);
	          return getSubmatrixRecursive(data[dimIndex], depth + 1);
	        });
	      } else {
	        return _mapIndex(dims, dimIndex => {
	          (0, _array.validateIndex)(dimIndex, data.length);
	          return data[dimIndex];
	        });
	      }
	    }
	  }

	  /**
	   * Replace a submatrix in this matrix
	   * Indexes are zero-based.
	   * @memberof DenseMatrix
	   * @param {DenseMatrix} matrix
	   * @param {Index} index
	   * @param {DenseMatrix | Array | *} submatrix
	   * @param {*} defaultValue          Default value, filled in on new entries when
	   *                                  the matrix is resized.
	   * @return {DenseMatrix} matrix
	   * @private
	   */
	  function _set(matrix, index, submatrix, defaultValue) {
	    if (!index || index.isIndex !== true) {
	      throw new TypeError('Invalid index');
	    }

	    // get index size and check whether the index contains a single value
	    const iSize = index.size();
	    const isScalar = index.isScalar();

	    // calculate the size of the submatrix, and convert it into an Array if needed
	    let submatrixSize;
	    if ((0, _is.isMatrix)(submatrix)) {
	      submatrixSize = submatrix.size();
	      submatrix = submatrix.valueOf();
	    } else {
	      submatrixSize = (0, _array.arraySize)(submatrix);
	    }
	    if (isScalar) {
	      // set a scalar

	      // check whether submatrix is a scalar
	      if (submatrixSize.length !== 0) {
	        throw new TypeError('Scalar expected');
	      }
	      matrix.set(index.min(), submatrix, defaultValue);
	    } else {
	      // set a submatrix

	      // broadcast submatrix
	      if (!(0, _object.deepStrictEqual)(submatrixSize, iSize)) {
	        if (submatrixSize.length === 0) {
	          submatrix = (0, _array.broadcastTo)([submatrix], iSize);
	        } else {
	          try {
	            submatrix = (0, _array.broadcastTo)(submatrix, iSize);
	          } catch (error) {}
	        }
	        submatrixSize = (0, _array.arraySize)(submatrix);
	      }

	      // validate dimensions
	      if (iSize.length < matrix._size.length) {
	        throw new _DimensionError.DimensionError(iSize.length, matrix._size.length, '<');
	      }
	      if (submatrixSize.length < iSize.length) {
	        // calculate number of missing outer dimensions
	        let i = 0;
	        let outer = 0;
	        while (iSize[i] === 1 && submatrixSize[i] === 1) {
	          i++;
	        }
	        while (iSize[i] === 1) {
	          outer++;
	          i++;
	        }

	        // unsqueeze both outer and inner dimensions
	        submatrix = (0, _array.unsqueeze)(submatrix, iSize.length, outer, submatrixSize);
	      }

	      // check whether the size of the submatrix matches the index size
	      if (!(0, _object.deepStrictEqual)(iSize, submatrixSize)) {
	        throw new _DimensionError.DimensionError(iSize, submatrixSize, '>');
	      }

	      // enlarge matrix when needed
	      const size = index.max().map(function (i) {
	        return i + 1;
	      });
	      _fit(matrix, size, defaultValue);

	      // insert the sub matrix
	      _setSubmatrix(matrix._data, index, submatrix);
	    }
	    return matrix;
	  }

	  /**
	   * Replace a submatrix of a multi dimensional matrix.
	   * @memberof DenseMatrix
	   * @param {Array} data
	   * @param {Index} index
	   * @param {Array} submatrix
	   * @private
	   */
	  function _setSubmatrix(data, index, submatrix) {
	    const maxDepth = index.size().length - 1;
	    setSubmatrixRecursive(data, submatrix);
	    function setSubmatrixRecursive(data, submatrix) {
	      let depth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
	      const range = index.dimension(depth);
	      const recursiveCallback = (rangeIndex, i) => {
	        (0, _array.validateIndex)(rangeIndex, data.length);
	        setSubmatrixRecursive(data[rangeIndex], submatrix[i[0]], depth + 1);
	      };
	      const finalCallback = (rangeIndex, i) => {
	        (0, _array.validateIndex)(rangeIndex, data.length);
	        data[rangeIndex] = submatrix[i[0]];
	      };
	      if (depth < maxDepth) {
	        if ((0, _is.isNumber)(range)) recursiveCallback(range, [0]);else range.forEach(recursiveCallback);
	      } else {
	        if ((0, _is.isNumber)(range)) finalCallback(range, [0]);else range.forEach(finalCallback);
	      }
	    }
	  }

	  /**
	   * Resize the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (resize in place).
	   *
	   * @memberof DenseMatrix
	   * @param {number[] || Matrix} size The new size the matrix should have.
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries.
	   *                                  If not provided, the matrix elements will
	   *                                  be filled with zeros.
	   * @param {boolean} [copy]          Return a resized copy of the matrix
	   *
	   * @return {Matrix}                 The resized matrix
	   */
	  DenseMatrix.prototype.resize = function (size, defaultValue, copy) {
	    // validate arguments
	    if (!(0, _is.isCollection)(size)) {
	      throw new TypeError('Array or Matrix expected');
	    }

	    // SparseMatrix input is always 2d, flatten this into 1d if it's indeed a vector
	    const sizeArray = size.valueOf().map(value => {
	      return Array.isArray(value) && value.length === 1 ? value[0] : value;
	    });

	    // matrix to resize
	    const m = copy ? this.clone() : this;
	    // resize matrix
	    return _resize(m, sizeArray, defaultValue);
	  };
	  function _resize(matrix, size, defaultValue) {
	    // check size
	    if (size.length === 0) {
	      // first value in matrix
	      let v = matrix._data;
	      // go deep
	      while ((0, _is.isArray)(v)) {
	        v = v[0];
	      }
	      return v;
	    }
	    // resize matrix
	    matrix._size = size.slice(0); // copy the array
	    matrix._data = (0, _array.resize)(matrix._data, matrix._size, defaultValue);
	    // return matrix
	    return matrix;
	  }

	  /**
	   * Reshape the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (reshape in place).
	   *
	   * NOTE: This might be better suited to copy by default, instead of modifying
	   *       in place. For now, it operates in place to remain consistent with
	   *       resize().
	   *
	   * @memberof DenseMatrix
	   * @param {number[]} size           The new size the matrix should have.
	   * @param {boolean} [copy]          Return a reshaped copy of the matrix
	   *
	   * @return {Matrix}                 The reshaped matrix
	   */
	  DenseMatrix.prototype.reshape = function (size, copy) {
	    const m = copy ? this.clone() : this;
	    m._data = (0, _array.reshape)(m._data, size);
	    const currentLength = m._size.reduce((length, size) => length * size);
	    m._size = (0, _array.processSizesWildcard)(size, currentLength);
	    return m;
	  };

	  /**
	   * Enlarge the matrix when it is smaller than given size.
	   * If the matrix is larger or equal sized, nothing is done.
	   * @memberof DenseMatrix
	   * @param {DenseMatrix} matrix           The matrix to be resized
	   * @param {number[]} size
	   * @param {*} defaultValue          Default value, filled in on new entries.
	   * @private
	   */
	  function _fit(matrix, size, defaultValue) {
	    const
	    // copy the array
	    newSize = matrix._size.slice(0);
	    let changed = false;

	    // add dimensions when needed
	    while (newSize.length < size.length) {
	      newSize.push(0);
	      changed = true;
	    }

	    // enlarge size when needed
	    for (let i = 0, ii = size.length; i < ii; i++) {
	      if (size[i] > newSize[i]) {
	        newSize[i] = size[i];
	        changed = true;
	      }
	    }
	    if (changed) {
	      // resize only when size is changed
	      _resize(matrix, newSize, defaultValue);
	    }
	  }

	  /**
	   * Create a clone of the matrix
	   * @memberof DenseMatrix
	   * @return {DenseMatrix} clone
	   */
	  DenseMatrix.prototype.clone = function () {
	    const m = new DenseMatrix({
	      data: (0, _object.clone)(this._data),
	      size: (0, _object.clone)(this._size),
	      datatype: this._datatype
	    });
	    return m;
	  };

	  /**
	   * Retrieve the size of the matrix.
	   * @memberof DenseMatrix
	   * @returns {number[]} size
	   */
	  DenseMatrix.prototype.size = function () {
	    return this._size.slice(0); // return a clone of _size
	  };

	  /**
	   * Create a new matrix with the results of the callback function executed on
	   * each entry of the matrix.
	   * @memberof DenseMatrix
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   * @param {boolean} skipZeros   If true, the callback function is invoked only for non-zero entries
	   * @param {boolean} isUnary     If true, the callback function is invoked with one parameter
	   *
	   * @return {DenseMatrix} matrix
	   */
	  DenseMatrix.prototype.map = function (callback) {
	    let isUnary = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
	    const me = this;
	    const maxDepth = me._size.length - 1;
	    if (maxDepth < 0) return me.clone();
	    const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, me, 'map', isUnary);
	    const fastCallbackFn = fastCallback.fn;
	    const result = me.create(undefined, me._datatype);
	    result._size = me._size;
	    if (isUnary || fastCallback.isUnary) {
	      result._data = iterateUnary(me._data);
	      return result;
	    }
	    if (maxDepth === 0) {
	      const inputData = me.valueOf();
	      const data = Array(inputData.length);
	      for (let i = 0; i < inputData.length; i++) {
	        data[i] = fastCallbackFn(inputData[i], [i], me);
	      }
	      result._data = data;
	      return result;
	    }
	    const index = [];
	    result._data = iterate(me._data);
	    return result;
	    function iterate(data) {
	      let depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	      const result = Array(data.length);
	      if (depth < maxDepth) {
	        for (let i = 0; i < data.length; i++) {
	          index[depth] = i;
	          result[i] = iterate(data[i], depth + 1);
	        }
	      } else {
	        for (let i = 0; i < data.length; i++) {
	          index[depth] = i;
	          result[i] = fastCallbackFn(data[i], index.slice(), me);
	        }
	      }
	      return result;
	    }
	    function iterateUnary(data) {
	      let depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	      const result = Array(data.length);
	      if (depth < maxDepth) {
	        for (let i = 0; i < data.length; i++) {
	          result[i] = iterateUnary(data[i], depth + 1);
	        }
	      } else {
	        for (let i = 0; i < data.length; i++) {
	          result[i] = fastCallbackFn(data[i]);
	        }
	      }
	      return result;
	    }
	  };

	  /**
	   * Execute a callback function on each entry of the matrix.
	   * @memberof DenseMatrix
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   * @param {boolean} skipZeros   If true, the callback function is invoked only for non-zero entries
	   * @param {boolean} isUnary     If true, the callback function is invoked with one parameter
	   */
	  DenseMatrix.prototype.forEach = function (callback) {
	    let isUnary = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
	    const me = this;
	    const maxDepth = me._size.length - 1;
	    if (maxDepth < 0) return;
	    const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, me, 'map', isUnary);
	    const fastCallbackFn = fastCallback.fn;
	    if (isUnary || fastCallback.isUnary) {
	      iterateUnary(me._data);
	      return;
	    }
	    if (maxDepth === 0) {
	      for (let i = 0; i < me._data.length; i++) {
	        fastCallbackFn(me._data[i], [i], me);
	      }
	      return;
	    }
	    const index = [];
	    iterate(me._data);
	    function iterate(data) {
	      let depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	      if (depth < maxDepth) {
	        for (let i = 0; i < data.length; i++) {
	          index[depth] = i;
	          iterate(data[i], depth + 1);
	        }
	      } else {
	        for (let i = 0; i < data.length; i++) {
	          index[depth] = i;
	          fastCallbackFn(data[i], index.slice(), me);
	        }
	      }
	    }
	    function iterateUnary(data) {
	      let depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	      if (depth < maxDepth) {
	        for (let i = 0; i < data.length; i++) {
	          iterateUnary(data[i], depth + 1);
	        }
	      } else {
	        for (let i = 0; i < data.length; i++) {
	          fastCallbackFn(data[i]);
	        }
	      }
	    }
	  };

	  /**
	   * Iterate over the matrix elements
	   * @return {Iterable<{ value, index: number[] }>}
	   */
	  DenseMatrix.prototype[Symbol.iterator] = function* () {
	    const maxDepth = this._size.length - 1;
	    if (maxDepth < 0) {
	      return;
	    }
	    if (maxDepth === 0) {
	      for (let i = 0; i < this._data.length; i++) {
	        yield {
	          value: this._data[i],
	          index: [i]
	        };
	      }
	      return;
	    }

	    // Multi-dimensional matrix: iterate over all elements
	    const index = Array(maxDepth + 1).fill(0);
	    const totalElements = this._size.reduce((a, b) => a * b, 1);
	    for (let count = 0; count < totalElements; count++) {
	      // Traverse to the current element using indices
	      let current = this._data;
	      for (let d = 0; d < maxDepth; d++) {
	        current = current[index[d]];
	      }
	      yield {
	        value: current[index[maxDepth]],
	        index: index.slice()
	      };

	      // Increment indices for next element
	      for (let d = maxDepth; d >= 0; d--) {
	        index[d]++;
	        if (index[d] < this._size[d]) break;
	        index[d] = 0;
	      }
	    }
	  };

	  /**
	   * Returns an array containing the rows of a 2D matrix
	   * @returns {Array<Matrix>}
	   */
	  DenseMatrix.prototype.rows = function () {
	    const result = [];
	    const s = this.size();
	    if (s.length !== 2) {
	      throw new TypeError('Rows can only be returned for a 2D matrix.');
	    }
	    const data = this._data;
	    for (const row of data) {
	      result.push(new DenseMatrix([row], this._datatype));
	    }
	    return result;
	  };

	  /**
	   * Returns an array containing the columns of a 2D matrix
	   * @returns {Array<Matrix>}
	   */
	  DenseMatrix.prototype.columns = function () {
	    const result = [];
	    const s = this.size();
	    if (s.length !== 2) {
	      throw new TypeError('Rows can only be returned for a 2D matrix.');
	    }
	    const data = this._data;
	    for (let i = 0; i < s[1]; i++) {
	      const col = data.map(row => [row[i]]);
	      result.push(new DenseMatrix(col, this._datatype));
	    }
	    return result;
	  };

	  /**
	   * Create an Array with a copy of the data of the DenseMatrix
	   * @memberof DenseMatrix
	   * @returns {Array} array
	   */
	  DenseMatrix.prototype.toArray = function () {
	    return (0, _object.clone)(this._data);
	  };

	  /**
	   * Get the primitive value of the DenseMatrix: a multidimensional array
	   * @memberof DenseMatrix
	   * @returns {Array} array
	   */
	  DenseMatrix.prototype.valueOf = function () {
	    return this._data;
	  };

	  /**
	   * Get a string representation of the matrix, with optional formatting options.
	   * @memberof DenseMatrix
	   * @param {Object | number | Function} [options]  Formatting options. See
	   *                                                lib/utils/number:format for a
	   *                                                description of the available
	   *                                                options.
	   * @returns {string} str
	   */
	  DenseMatrix.prototype.format = function (options) {
	    return (0, _string.format)(this._data, options);
	  };

	  /**
	   * Get a string representation of the matrix
	   * @memberof DenseMatrix
	   * @returns {string} str
	   */
	  DenseMatrix.prototype.toString = function () {
	    return (0, _string.format)(this._data);
	  };

	  /**
	   * Get a JSON representation of the matrix
	   * @memberof DenseMatrix
	   * @returns {Object}
	   */
	  DenseMatrix.prototype.toJSON = function () {
	    return {
	      mathjs: 'DenseMatrix',
	      data: this._data,
	      size: this._size,
	      datatype: this._datatype
	    };
	  };

	  /**
	   * Get the kth Matrix diagonal.
	   *
	   * @memberof DenseMatrix
	   * @param {number | BigNumber} [k=0]     The kth diagonal where the vector will retrieved.
	   *
	   * @returns {Matrix}                     The matrix with the diagonal values.
	   */
	  DenseMatrix.prototype.diagonal = function (k) {
	    // validate k if any
	    if (k) {
	      // convert BigNumber to a number
	      if ((0, _is.isBigNumber)(k)) {
	        k = k.toNumber();
	      }
	      // is must be an integer
	      if (!(0, _is.isNumber)(k) || !(0, _number.isInteger)(k)) {
	        throw new TypeError('The parameter k must be an integer number');
	      }
	    } else {
	      // default value
	      k = 0;
	    }
	    const kSuper = k > 0 ? k : 0;
	    const kSub = k < 0 ? -k : 0;

	    // rows & columns
	    const rows = this._size[0];
	    const columns = this._size[1];

	    // number diagonal values
	    const n = Math.min(rows - kSub, columns - kSuper);

	    // x is a matrix get diagonal from matrix
	    const data = [];

	    // loop rows
	    for (let i = 0; i < n; i++) {
	      data[i] = this._data[i + kSub][i + kSuper];
	    }

	    // create DenseMatrix
	    return new DenseMatrix({
	      data,
	      size: [n],
	      datatype: this._datatype
	    });
	  };

	  /**
	   * Create a diagonal matrix.
	   *
	   * @memberof DenseMatrix
	   * @param {Array} size                     The matrix size.
	   * @param {number | Matrix | Array } value The values for the diagonal.
	   * @param {number | BigNumber} [k=0]       The kth diagonal where the vector will be filled in.
	   * @param {number} [defaultValue]          The default value for non-diagonal
	   * @param {string} [datatype]              The datatype for the diagonal
	   *
	   * @returns {DenseMatrix}
	   */
	  DenseMatrix.diagonal = function (size, value, k, defaultValue) {
	    if (!(0, _is.isArray)(size)) {
	      throw new TypeError('Array expected, size parameter');
	    }
	    if (size.length !== 2) {
	      throw new Error('Only two dimensions matrix are supported');
	    }

	    // map size & validate
	    size = size.map(function (s) {
	      // check it is a big number
	      if ((0, _is.isBigNumber)(s)) {
	        // convert it
	        s = s.toNumber();
	      }
	      // validate arguments
	      if (!(0, _is.isNumber)(s) || !(0, _number.isInteger)(s) || s < 1) {
	        throw new Error('Size values must be positive integers');
	      }
	      return s;
	    });

	    // validate k if any
	    if (k) {
	      // convert BigNumber to a number
	      if ((0, _is.isBigNumber)(k)) {
	        k = k.toNumber();
	      }
	      // is must be an integer
	      if (!(0, _is.isNumber)(k) || !(0, _number.isInteger)(k)) {
	        throw new TypeError('The parameter k must be an integer number');
	      }
	    } else {
	      // default value
	      k = 0;
	    }
	    const kSuper = k > 0 ? k : 0;
	    const kSub = k < 0 ? -k : 0;

	    // rows and columns
	    const rows = size[0];
	    const columns = size[1];

	    // number of non-zero items
	    const n = Math.min(rows - kSub, columns - kSuper);

	    // value extraction function
	    let _value;

	    // check value
	    if ((0, _is.isArray)(value)) {
	      // validate array
	      if (value.length !== n) {
	        // number of values in array must be n
	        throw new Error('Invalid value array length');
	      }
	      // define function
	      _value = function (i) {
	        // return value @ i
	        return value[i];
	      };
	    } else if ((0, _is.isMatrix)(value)) {
	      // matrix size
	      const ms = value.size();
	      // validate matrix
	      if (ms.length !== 1 || ms[0] !== n) {
	        // number of values in array must be n
	        throw new Error('Invalid matrix length');
	      }
	      // define function
	      _value = function (i) {
	        // return value @ i
	        return value.get([i]);
	      };
	    } else {
	      // define function
	      _value = function () {
	        // return value
	        return value;
	      };
	    }

	    // discover default value if needed
	    if (!defaultValue) {
	      // check first value in array
	      defaultValue = (0, _is.isBigNumber)(_value(0)) ? _value(0).mul(0) // trick to create a BigNumber with value zero
	      : 0;
	    }

	    // empty array
	    let data = [];

	    // check we need to resize array
	    if (size.length > 0) {
	      // resize array
	      data = (0, _array.resize)(data, size, defaultValue);
	      // fill diagonal
	      for (let d = 0; d < n; d++) {
	        data[d + kSub][d + kSuper] = _value(d);
	      }
	    }

	    // create DenseMatrix
	    return new DenseMatrix({
	      data,
	      size: [rows, columns]
	    });
	  };

	  /**
	   * Generate a matrix from a JSON object
	   * @memberof DenseMatrix
	   * @param {Object} json  An object structured like
	   *                       `{"mathjs": "DenseMatrix", data: [], size: []}`,
	   *                       where mathjs is optional
	   * @returns {DenseMatrix}
	   */
	  DenseMatrix.fromJSON = function (json) {
	    return new DenseMatrix(json);
	  };

	  /**
	   * Swap rows i and j in Matrix.
	   *
	   * @memberof DenseMatrix
	   * @param {number} i       Matrix row index 1
	   * @param {number} j       Matrix row index 2
	   *
	   * @return {Matrix}        The matrix reference
	   */
	  DenseMatrix.prototype.swapRows = function (i, j) {
	    // check index
	    if (!(0, _is.isNumber)(i) || !(0, _number.isInteger)(i) || !(0, _is.isNumber)(j) || !(0, _number.isInteger)(j)) {
	      throw new Error('Row index must be positive integers');
	    }
	    // check dimensions
	    if (this._size.length !== 2) {
	      throw new Error('Only two dimensional matrix is supported');
	    }
	    // validate index
	    (0, _array.validateIndex)(i, this._size[0]);
	    (0, _array.validateIndex)(j, this._size[0]);

	    // swap rows
	    DenseMatrix._swapRows(i, j, this._data);
	    // return current instance
	    return this;
	  };

	  /**
	   * Swap rows i and j in Dense Matrix data structure.
	   *
	   * @param {number} i       Matrix row index 1
	   * @param {number} j       Matrix row index 2
	   * @param {Array} data     Matrix data
	   */
	  DenseMatrix._swapRows = function (i, j, data) {
	    // swap values i <-> j
	    const vi = data[i];
	    data[i] = data[j];
	    data[j] = vi;
	  };

	  /**
	   * Preprocess data, which can be an Array or DenseMatrix with nested Arrays and
	   * Matrices. Clones all (nested) Arrays, and replaces all nested Matrices with Arrays
	   * @memberof DenseMatrix
	   * @param {Array | Matrix} data
	   * @return {Array} data
	   */
	  function preprocess(data) {
	    if ((0, _is.isMatrix)(data)) {
	      return preprocess(data.valueOf());
	    }
	    if ((0, _is.isArray)(data)) {
	      return data.map(preprocess);
	    }
	    return data;
	  }
	  return DenseMatrix;
	}, {
	  isClass: true
	});
	return DenseMatrix;
}

var clone$1 = {};

var hasRequiredClone$1;

function requireClone$1 () {
	if (hasRequiredClone$1) return clone$1;
	hasRequiredClone$1 = 1;

	Object.defineProperty(clone$1, "__esModule", {
	  value: true
	});
	clone$1.createClone = void 0;
	var _object = requireObject$3();
	var _factory = requireFactory$b();
	const name = 'clone';
	const dependencies = ['typed'];
	clone$1.createClone = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Clone an object. Will make a deep copy of the data.
	   *
	   * Syntax:
	   *
	   *     math.clone(x)
	   *
	   * Examples:
	   *
	   *    math.clone(3.5)                   // returns number 3.5
	   *    math.clone(math.complex('2-4i'))  // returns Complex 2 - 4i
	   *    math.clone(math.unit(45, 'deg'))  // returns Unit 45 deg
	   *    math.clone([[1, 2], [3, 4]])      // returns Array [[1, 2], [3, 4]]
	   *    math.clone("hello world")         // returns string "hello world"
	   *
	   * @param {*} x   Object to be cloned
	   * @return {*} A clone of object x
	   */
	  return typed(name, {
	    any: _object.clone
	  });
	});
	return clone$1;
}

var isInteger$1 = {};

var collection = {};

var _switch = {};

var hasRequired_switch;

function require_switch () {
	if (hasRequired_switch) return _switch;
	hasRequired_switch = 1;

	Object.defineProperty(_switch, "__esModule", {
	  value: true
	});
	_switch._switch = _switch$1;
	/**
	 * Transpose a matrix
	 * @param {Array} mat
	 * @returns {Array} ret
	 * @private
	 */
	function _switch$1(mat) {
	  const I = mat.length;
	  const J = mat[0].length;
	  let i, j;
	  const ret = [];
	  for (j = 0; j < J; j++) {
	    const tmp = [];
	    for (i = 0; i < I; i++) {
	      tmp.push(mat[i][j]);
	    }
	    ret.push(tmp);
	  }
	  return ret;
	}
	return _switch;
}

var hasRequiredCollection;

function requireCollection () {
	if (hasRequiredCollection) return collection;
	hasRequiredCollection = 1;

	Object.defineProperty(collection, "__esModule", {
	  value: true
	});
	collection.containsCollections = containsCollections;
	collection.deepForEach = deepForEach;
	collection.deepMap = deepMap;
	collection.reduce = reduce;
	collection.scatter = scatter;
	var _is = requireIs();
	var _IndexError = requireIndexError();
	var _array = requireArray();
	var _switch2 = require_switch();
	/**
	 * Test whether an array contains collections
	 * @param {Array} array
	 * @returns {boolean} Returns true when the array contains one or multiple
	 *                    collections (Arrays or Matrices). Returns false otherwise.
	 */
	function containsCollections(array) {
	  for (let i = 0; i < array.length; i++) {
	    if ((0, _is.isCollection)(array[i])) {
	      return true;
	    }
	  }
	  return false;
	}

	/**
	 * Recursively loop over all elements in a given multi dimensional array
	 * and invoke the callback on each of the elements.
	 * @param {Array | Matrix} array
	 * @param {Function} callback     The callback method is invoked with one
	 *                                parameter: the current element in the array
	 */
	function deepForEach(array, callback) {
	  if ((0, _is.isMatrix)(array)) {
	    array.forEach(x => callback(x), false, true);
	  } else {
	    (0, _array.deepForEach)(array, callback, true);
	  }
	}

	/**
	 * Execute the callback function element wise for each element in array and any
	 * nested array
	 * Returns an array with the results
	 * @param {Array | Matrix} array
	 * @param {Function} callback   The callback is called with two parameters:
	 *                              value1 and value2, which contain the current
	 *                              element of both arrays.
	 * @param {boolean} [skipZeros] Invoke callback function for non-zero values only.
	 *
	 * @return {Array | Matrix} res
	 */
	function deepMap(array, callback, skipZeros) {
	  if (!skipZeros) {
	    if ((0, _is.isMatrix)(array)) {
	      return array.map(x => callback(x), false, true);
	    } else {
	      return (0, _array.deepMap)(array, callback, true);
	    }
	  }
	  const skipZerosCallback = x => x === 0 ? x : callback(x);
	  if ((0, _is.isMatrix)(array)) {
	    return array.map(x => skipZerosCallback(x), false, true);
	  } else {
	    return (0, _array.deepMap)(array, skipZerosCallback, true);
	  }
	}

	/**
	 * Reduce a given matrix or array to a new matrix or
	 * array with one less dimension, applying the given
	 * callback in the selected dimension.
	 * @param {Array | Matrix} mat
	 * @param {number} dim
	 * @param {Function} callback
	 * @return {Array | Matrix} res
	 */
	function reduce(mat, dim, callback) {
	  const size = Array.isArray(mat) ? (0, _array.arraySize)(mat) : mat.size();
	  if (dim < 0 || dim >= size.length) {
	    // TODO: would be more clear when throwing a DimensionError here
	    throw new _IndexError.IndexError(dim, size.length);
	  }
	  if ((0, _is.isMatrix)(mat)) {
	    return mat.create(_reduce(mat.valueOf(), dim, callback), mat.datatype());
	  } else {
	    return _reduce(mat, dim, callback);
	  }
	}

	/**
	 * Recursively reduce a matrix
	 * @param {Array} mat
	 * @param {number} dim
	 * @param {Function} callback
	 * @returns {Array} ret
	 * @private
	 */
	function _reduce(mat, dim, callback) {
	  let i, ret, val, tran;
	  if (dim <= 0) {
	    if (!Array.isArray(mat[0])) {
	      val = mat[0];
	      for (i = 1; i < mat.length; i++) {
	        val = callback(val, mat[i]);
	      }
	      return val;
	    } else {
	      tran = (0, _switch2._switch)(mat);
	      ret = [];
	      for (i = 0; i < tran.length; i++) {
	        ret[i] = _reduce(tran[i], dim - 1, callback);
	      }
	      return ret;
	    }
	  } else {
	    ret = [];
	    for (i = 0; i < mat.length; i++) {
	      ret[i] = _reduce(mat[i], dim - 1, callback);
	    }
	    return ret;
	  }
	}

	// TODO: document function scatter
	function scatter(a, j, w, x, u, mark, cindex, f, inverse, update, value) {
	  // a arrays
	  const avalues = a._values;
	  const aindex = a._index;
	  const aptr = a._ptr;

	  // vars
	  let k, k0, k1, i;

	  // check we need to process values (pattern matrix)
	  if (x) {
	    // values in j
	    for (k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	      // row
	      i = aindex[k];
	      // check value exists in current j
	      if (w[i] !== mark) {
	        // i is new entry in j
	        w[i] = mark;
	        // add i to pattern of C
	        cindex.push(i);
	        // x(i) = A, check we need to call function this time
	        if (update) {
	          // copy value to workspace calling callback function
	          x[i] = inverse ? f(avalues[k], value) : f(value, avalues[k]);
	          // function was called on current row
	          u[i] = mark;
	        } else {
	          // copy value to workspace
	          x[i] = avalues[k];
	        }
	      } else {
	        // i exists in C already
	        x[i] = inverse ? f(avalues[k], x[i]) : f(x[i], avalues[k]);
	        // function was called on current row
	        u[i] = mark;
	      }
	    }
	  } else {
	    // values in j
	    for (k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	      // row
	      i = aindex[k];
	      // check value exists in current j
	      if (w[i] !== mark) {
	        // i is new entry in j
	        w[i] = mark;
	        // add i to pattern of C
	        cindex.push(i);
	      } else {
	        // indicate function was called on current row
	        u[i] = mark;
	      }
	    }
	  }
	}
	return collection;
}

var hasRequiredIsInteger$1;

function requireIsInteger$1 () {
	if (hasRequiredIsInteger$1) return isInteger$1;
	hasRequiredIsInteger$1 = 1;

	Object.defineProperty(isInteger$1, "__esModule", {
	  value: true
	});
	isInteger$1.createIsInteger = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'isInteger';
	const dependencies = ['typed', 'equal'];
	isInteger$1.createIsInteger = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equal
	  } = _ref;
	  /**
	   * Test whether a value is an integer number.
	   * The function supports `number`, `BigNumber`, and `Fraction`.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isInteger(x)
	   *
	   * Examples:
	   *
	   *    math.isInteger(2)                     // returns true
	   *    math.isInteger(0)                     // returns true
	   *    math.isInteger(0.5)                   // returns false
	   *    math.isInteger(math.bignumber(500))   // returns true
	   *    math.isInteger(math.fraction(4))      // returns true
	   *    math.isInteger('3')                   // returns true
	   *    math.isInteger([3, 0.5, -2])          // returns [true, false, true]
	   *    math.isInteger(math.complex('2-4i'))  // throws TypeError
	   *
	   * See also:
	   *
	   *    isNumeric, isPositive, isNegative, isZero
	   *
	   * @param {number | BigNumber | bigint | Fraction | Array | Matrix} x   Value to be tested
	   * @return {boolean}  Returns true when `x` contains a numeric, integer value.
	   *                    Throws an error in case of an unknown data type.
	   */
	  return typed(name, {
	    number: n => Number.isFinite(n) ? equal(n, Math.round(n)) : false,
	    BigNumber: b => b.isFinite() ? equal(b.round(), b) : false,
	    bigint: b => true,
	    Fraction: r => r.d === 1n,
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return isInteger$1;
}

var isNegative$1 = {};

var number$2 = {};

var arithmetic = {};

var hasRequiredArithmetic;

function requireArithmetic () {
	if (hasRequiredArithmetic) return arithmetic;
	hasRequiredArithmetic = 1;

	Object.defineProperty(arithmetic, "__esModule", {
	  value: true
	});
	arithmetic.absNumber = absNumber;
	arithmetic.addNumber = addNumber;
	arithmetic.cbrtNumber = cbrtNumber;
	arithmetic.cubeNumber = cubeNumber;
	arithmetic.divideNumber = divideNumber;
	arithmetic.expNumber = expNumber;
	arithmetic.expm1Number = expm1Number;
	arithmetic.gcdNumber = gcdNumber;
	arithmetic.lcmNumber = lcmNumber;
	arithmetic.log10Number = log10Number;
	arithmetic.log1pNumber = log1pNumber;
	arithmetic.log2Number = log2Number;
	arithmetic.logNumber = logNumber;
	arithmetic.modNumber = modNumber;
	arithmetic.multiplyNumber = multiplyNumber;
	arithmetic.normNumber = normNumber;
	arithmetic.nthRootNumber = nthRootNumber;
	arithmetic.powNumber = powNumber;
	arithmetic.roundNumber = roundNumber;
	arithmetic.signNumber = signNumber;
	arithmetic.sqrtNumber = sqrtNumber;
	arithmetic.squareNumber = squareNumber;
	arithmetic.subtractNumber = subtractNumber;
	arithmetic.unaryMinusNumber = unaryMinusNumber;
	arithmetic.unaryPlusNumber = unaryPlusNumber;
	arithmetic.xgcdNumber = xgcdNumber;
	var _number = requireNumber$3();
	const n1 = 'number';
	const n2 = 'number, number';
	function absNumber(a) {
	  return Math.abs(a);
	}
	absNumber.signature = n1;
	function addNumber(a, b) {
	  return a + b;
	}
	addNumber.signature = n2;
	function subtractNumber(a, b) {
	  return a - b;
	}
	subtractNumber.signature = n2;
	function multiplyNumber(a, b) {
	  return a * b;
	}
	multiplyNumber.signature = n2;
	function divideNumber(a, b) {
	  return a / b;
	}
	divideNumber.signature = n2;
	function unaryMinusNumber(x) {
	  return -x;
	}
	unaryMinusNumber.signature = n1;
	function unaryPlusNumber(x) {
	  return x;
	}
	unaryPlusNumber.signature = n1;
	function cbrtNumber(x) {
	  return (0, _number.cbrt)(x);
	}
	cbrtNumber.signature = n1;
	function cubeNumber(x) {
	  return x * x * x;
	}
	cubeNumber.signature = n1;
	function expNumber(x) {
	  return Math.exp(x);
	}
	expNumber.signature = n1;
	function expm1Number(x) {
	  return (0, _number.expm1)(x);
	}
	expm1Number.signature = n1;

	/**
	 * Calculate gcd for numbers
	 * @param {number} a
	 * @param {number} b
	 * @returns {number} Returns the greatest common denominator of a and b
	 */
	function gcdNumber(a, b) {
	  if (!(0, _number.isInteger)(a) || !(0, _number.isInteger)(b)) {
	    throw new Error('Parameters in function gcd must be integer numbers');
	  }

	  // https://en.wikipedia.org/wiki/Euclidean_algorithm
	  let r;
	  while (b !== 0) {
	    r = a % b;
	    a = b;
	    b = r;
	  }
	  return a < 0 ? -a : a;
	}
	gcdNumber.signature = n2;

	/**
	 * Calculate lcm for two numbers
	 * @param {number} a
	 * @param {number} b
	 * @returns {number} Returns the least common multiple of a and b
	 */
	function lcmNumber(a, b) {
	  if (!(0, _number.isInteger)(a) || !(0, _number.isInteger)(b)) {
	    throw new Error('Parameters in function lcm must be integer numbers');
	  }
	  if (a === 0 || b === 0) {
	    return 0;
	  }

	  // https://en.wikipedia.org/wiki/Euclidean_algorithm
	  // evaluate lcm here inline to reduce overhead
	  let t;
	  const prod = a * b;
	  while (b !== 0) {
	    t = b;
	    b = a % t;
	    a = t;
	  }
	  return Math.abs(prod / a);
	}
	lcmNumber.signature = n2;

	/**
	 * Calculate the logarithm of a value, optionally to a given base.
	 * @param {number} x
	 * @param {number | null | undefined} base
	 * @return {number}
	 */
	function logNumber(x, y) {
	  if (y) {
	    return Math.log(x) / Math.log(y);
	  }
	  return Math.log(x);
	}

	/**
	 * Calculate the 10-base logarithm of a number
	 * @param {number} x
	 * @return {number}
	 */
	function log10Number(x) {
	  return (0, _number.log10)(x);
	}
	log10Number.signature = n1;

	/**
	 * Calculate the 2-base logarithm of a number
	 * @param {number} x
	 * @return {number}
	 */
	function log2Number(x) {
	  return (0, _number.log2)(x);
	}
	log2Number.signature = n1;

	/**
	 * Calculate the natural logarithm of a `number+1`
	 * @param {number} x
	 * @returns {number}
	 */
	function log1pNumber(x) {
	  return (0, _number.log1p)(x);
	}
	log1pNumber.signature = n1;

	/**
	 * Calculate the modulus of two numbers
	 * @param {number} x
	 * @param {number} y
	 * @returns {number} res
	 * @private
	 */
	function modNumber(x, y) {
	  // We don't use JavaScript's % operator here as this doesn't work
	  // correctly for x < 0 and x === 0
	  // see https://en.wikipedia.org/wiki/Modulo_operation
	  return y === 0 ? x : x - y * Math.floor(x / y);
	}
	modNumber.signature = n2;

	/**
	 * Calculate the nth root of a, solve x^root == a
	 * http://rosettacode.org/wiki/Nth_root#JavaScript
	 * @param {number} a
	 * @param {number} [2] root
	 * @private
	 */
	function nthRootNumber(a) {
	  let root = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
	  const inv = root < 0;
	  if (inv) {
	    root = -root;
	  }
	  if (root === 0) {
	    throw new Error('Root must be non-zero');
	  }
	  if (a < 0 && Math.abs(root) % 2 !== 1) {
	    throw new Error('Root must be odd when a is negative.');
	  }

	  // edge cases zero and infinity
	  if (a === 0) {
	    return inv ? Infinity : 0;
	  }
	  if (!isFinite(a)) {
	    return inv ? 0 : a;
	  }
	  let x = Math.pow(Math.abs(a), 1 / root);
	  // If a < 0, we require that root is an odd integer,
	  // so (-1) ^ (1/root) = -1
	  x = a < 0 ? -x : x;
	  return inv ? 1 / x : x;

	  // Very nice algorithm, but fails with nthRoot(-2, 3).
	  // Newton's method has some well-known problems at times:
	  // https://en.wikipedia.org/wiki/Newton%27s_method#Failure_analysis
	  /*
	  let x = 1 // Initial guess
	  let xPrev = 1
	  let i = 0
	  const iMax = 10000
	  do {
	    const delta = (a / Math.pow(x, root - 1) - x) / root
	    xPrev = x
	    x = x + delta
	    i++
	  }
	  while (xPrev !== x && i < iMax)
	   if (xPrev !== x) {
	    throw new Error('Function nthRoot failed to converge')
	  }
	   return inv ? 1 / x : x
	  */
	}
	function signNumber(x) {
	  return (0, _number.sign)(x);
	}
	signNumber.signature = n1;
	function sqrtNumber(x) {
	  return Math.sqrt(x);
	}
	sqrtNumber.signature = n1;
	function squareNumber(x) {
	  return x * x;
	}
	squareNumber.signature = n1;

	/**
	 * Calculate xgcd for two numbers
	 * @param {number} a
	 * @param {number} b
	 * @return {number} result
	 * @private
	 */
	function xgcdNumber(a, b) {
	  // source: https://en.wikipedia.org/wiki/Extended_Euclidean_algorithm
	  let t; // used to swap two variables
	  let q; // quotient
	  let r; // remainder
	  let x = 0;
	  let lastx = 1;
	  let y = 1;
	  let lasty = 0;
	  if (!(0, _number.isInteger)(a) || !(0, _number.isInteger)(b)) {
	    throw new Error('Parameters in function xgcd must be integer numbers');
	  }
	  while (b) {
	    q = Math.floor(a / b);
	    r = a - q * b;
	    t = x;
	    x = lastx - q * x;
	    lastx = t;
	    t = y;
	    y = lasty - q * y;
	    lasty = t;
	    a = b;
	    b = r;
	  }
	  let res;
	  if (a < 0) {
	    res = [-a, -lastx, -lasty];
	  } else {
	    res = [a, a ? lastx : 0, lasty];
	  }
	  return res;
	}
	xgcdNumber.signature = n2;

	/**
	 * Calculates the power of x to y, x^y, for two numbers.
	 * @param {number} x
	 * @param {number} y
	 * @return {number} res
	 */
	function powNumber(x, y) {
	  // x^Infinity === 0 if -1 < x < 1
	  // A real number 0 is returned instead of complex(0)
	  if (x * x < 1 && y === Infinity || x * x > 1 && y === -Infinity) {
	    return 0;
	  }
	  return Math.pow(x, y);
	}
	powNumber.signature = n2;

	/**
	 * round a number to the given number of decimals, or to zero if decimals is
	 * not provided
	 * @param {number} value
	 * @param {number} decimals       number of decimals, between 0 and 15 (0 by default)
	 * @return {number} roundedValue
	 */
	function roundNumber(value) {
	  let decimals = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	  if (!(0, _number.isInteger)(decimals) || decimals < 0 || decimals > 15) {
	    throw new Error('Number of decimals in function round must be an integer from 0 to 15 inclusive');
	  }
	  return parseFloat((0, _number.toFixed)(value, decimals));
	}

	/**
	 * Calculate the norm of a number, the absolute value.
	 * @param {number} x
	 * @return {number}
	 */
	function normNumber(x) {
	  return Math.abs(x);
	}
	normNumber.signature = n1;
	return arithmetic;
}

var bitwise$1 = {};

var hasRequiredBitwise$1;

function requireBitwise$1 () {
	if (hasRequiredBitwise$1) return bitwise$1;
	hasRequiredBitwise$1 = 1;

	Object.defineProperty(bitwise$1, "__esModule", {
	  value: true
	});
	bitwise$1.bitAndNumber = bitAndNumber;
	bitwise$1.bitNotNumber = bitNotNumber;
	bitwise$1.bitOrNumber = bitOrNumber;
	bitwise$1.bitXorNumber = bitXorNumber;
	bitwise$1.leftShiftNumber = leftShiftNumber;
	bitwise$1.rightArithShiftNumber = rightArithShiftNumber;
	bitwise$1.rightLogShiftNumber = rightLogShiftNumber;
	var _number = requireNumber$3();
	const n1 = 'number';
	const n2 = 'number, number';
	function bitAndNumber(x, y) {
	  if (!(0, _number.isInteger)(x) || !(0, _number.isInteger)(y)) {
	    throw new Error('Integers expected in function bitAnd');
	  }
	  return x & y;
	}
	bitAndNumber.signature = n2;
	function bitNotNumber(x) {
	  if (!(0, _number.isInteger)(x)) {
	    throw new Error('Integer expected in function bitNot');
	  }
	  return ~x;
	}
	bitNotNumber.signature = n1;
	function bitOrNumber(x, y) {
	  if (!(0, _number.isInteger)(x) || !(0, _number.isInteger)(y)) {
	    throw new Error('Integers expected in function bitOr');
	  }
	  return x | y;
	}
	bitOrNumber.signature = n2;
	function bitXorNumber(x, y) {
	  if (!(0, _number.isInteger)(x) || !(0, _number.isInteger)(y)) {
	    throw new Error('Integers expected in function bitXor');
	  }
	  return x ^ y;
	}
	bitXorNumber.signature = n2;
	function leftShiftNumber(x, y) {
	  if (!(0, _number.isInteger)(x) || !(0, _number.isInteger)(y)) {
	    throw new Error('Integers expected in function leftShift');
	  }
	  return x << y;
	}
	leftShiftNumber.signature = n2;
	function rightArithShiftNumber(x, y) {
	  if (!(0, _number.isInteger)(x) || !(0, _number.isInteger)(y)) {
	    throw new Error('Integers expected in function rightArithShift');
	  }
	  return x >> y;
	}
	rightArithShiftNumber.signature = n2;
	function rightLogShiftNumber(x, y) {
	  if (!(0, _number.isInteger)(x) || !(0, _number.isInteger)(y)) {
	    throw new Error('Integers expected in function rightLogShift');
	  }
	  return x >>> y;
	}
	rightLogShiftNumber.signature = n2;
	return bitwise$1;
}

var combinations$2 = {};

var product = {};

var hasRequiredProduct;

function requireProduct () {
	if (hasRequiredProduct) return product;
	hasRequiredProduct = 1;

	Object.defineProperty(product, "__esModule", {
	  value: true
	});
	product.product = product$1;
	/** @param {number} i
	 *  @param {number} n
	 *  @returns {number} product of i to n
	 */
	function product$1(i, n) {
	  if (n < i) {
	    return 1;
	  }
	  if (n === i) {
	    return n;
	  }
	  const half = n + i >> 1; // divide (n + i) by 2 and truncate to integer
	  return product$1(i, half) * product$1(half + 1, n);
	}
	return product;
}

var hasRequiredCombinations$2;

function requireCombinations$2 () {
	if (hasRequiredCombinations$2) return combinations$2;
	hasRequiredCombinations$2 = 1;

	Object.defineProperty(combinations$2, "__esModule", {
	  value: true
	});
	combinations$2.combinationsNumber = combinationsNumber;
	var _number = requireNumber$3();
	var _product = requireProduct();
	function combinationsNumber(n, k) {
	  if (!(0, _number.isInteger)(n) || n < 0) {
	    throw new TypeError('Positive integer value expected in function combinations');
	  }
	  if (!(0, _number.isInteger)(k) || k < 0) {
	    throw new TypeError('Positive integer value expected in function combinations');
	  }
	  if (k > n) {
	    throw new TypeError('k must be less than or equal to n');
	  }
	  const nMinusk = n - k;
	  let answer = 1;
	  const firstnumerator = k < nMinusk ? nMinusk + 1 : k + 1;
	  let nextdivisor = 2;
	  const lastdivisor = k < nMinusk ? k : nMinusk;
	  // balance multiplications and divisions to try to keep intermediate values
	  // in exact-integer range as long as possible
	  for (let nextnumerator = firstnumerator; nextnumerator <= n; ++nextnumerator) {
	    answer *= nextnumerator;
	    while (nextdivisor <= lastdivisor && answer % nextdivisor === 0) {
	      answer /= nextdivisor;
	      ++nextdivisor;
	    }
	  }
	  // for big n, k, floating point may have caused weirdness in remainder
	  if (nextdivisor <= lastdivisor) {
	    answer /= (0, _product.product)(nextdivisor, lastdivisor);
	  }
	  return answer;
	}
	combinationsNumber.signature = 'number, number';
	return combinations$2;
}

var constants$4 = {};

var hasRequiredConstants$4;

function requireConstants$4 () {
	if (hasRequiredConstants$4) return constants$4;
	hasRequiredConstants$4 = 1;

	Object.defineProperty(constants$4, "__esModule", {
	  value: true
	});
	constants$4.tau = constants$4.pi = constants$4.phi = constants$4.e = void 0;
	constants$4.pi = Math.PI;
	constants$4.tau = 2 * Math.PI;
	constants$4.e = Math.E;
	constants$4.phi = 1.6180339887498948; // eslint-disable-line no-loss-of-precision
	return constants$4;
}

var logical = {};

var hasRequiredLogical;

function requireLogical () {
	if (hasRequiredLogical) return logical;
	hasRequiredLogical = 1;

	Object.defineProperty(logical, "__esModule", {
	  value: true
	});
	logical.andNumber = andNumber;
	logical.notNumber = notNumber;
	logical.orNumber = orNumber;
	logical.xorNumber = xorNumber;
	const n1 = 'number';
	const n2 = 'number, number';
	function notNumber(x) {
	  return !x;
	}
	notNumber.signature = n1;
	function orNumber(x, y) {
	  return !!(x || y);
	}
	orNumber.signature = n2;
	function xorNumber(x, y) {
	  return !!x !== !!y;
	}
	xorNumber.signature = n2;
	function andNumber(x, y) {
	  return !!(x && y);
	}
	andNumber.signature = n2;
	return logical;
}

var relational = {};

var hasRequiredRelational;

function requireRelational () {
	if (hasRequiredRelational) return relational;
	hasRequiredRelational = 1;
	return relational;
}

var probability = {};

var hasRequiredProbability;

function requireProbability () {
	if (hasRequiredProbability) return probability;
	hasRequiredProbability = 1;

	Object.defineProperty(probability, "__esModule", {
	  value: true
	});
	probability.gammaG = void 0;
	probability.gammaNumber = gammaNumber;
	probability.lgammaN = probability.lgammaG = probability.gammaP = void 0;
	probability.lgammaNumber = lgammaNumber;
	probability.lnSqrt2PI = probability.lgammaSeries = void 0;
	var _number = requireNumber$3();
	var _product = requireProduct();
	/* eslint-disable no-loss-of-precision */

	function gammaNumber(n) {
	  let x;
	  if ((0, _number.isInteger)(n)) {
	    if (n <= 0) {
	      return Number.isFinite(n) ? Infinity : NaN;
	    }
	    if (n > 171) {
	      return Infinity; // Will overflow
	    }
	    return (0, _product.product)(1, n - 1);
	  }
	  if (n < 0.5) {
	    return Math.PI / (Math.sin(Math.PI * n) * gammaNumber(1 - n));
	  }
	  if (n >= 171.35) {
	    return Infinity; // will overflow
	  }
	  if (n > 85.0) {
	    // Extended Stirling Approx
	    const twoN = n * n;
	    const threeN = twoN * n;
	    const fourN = threeN * n;
	    const fiveN = fourN * n;
	    return Math.sqrt(2 * Math.PI / n) * Math.pow(n / Math.E, n) * (1 + 1 / (12 * n) + 1 / (288 * twoN) - 139 / (51840 * threeN) - 571 / (2488320 * fourN) + 163879 / (209018880 * fiveN) + 5246819 / (75246796800 * fiveN * n));
	  }
	  --n;
	  x = gammaP[0];
	  for (let i = 1; i < gammaP.length; ++i) {
	    x += gammaP[i] / (n + i);
	  }
	  const t = n + gammaG + 0.5;
	  return Math.sqrt(2 * Math.PI) * Math.pow(t, n + 0.5) * Math.exp(-t) * x;
	}
	gammaNumber.signature = 'number';

	// TODO: comment on the variables g and p

	const gammaG = probability.gammaG = 4.7421875;
	const gammaP = probability.gammaP = [0.99999999999999709182, 57.156235665862923517, -59.597960355475491248, 14.136097974741747174, -0.49191381609762019978, 0.33994649984811888699e-4, 0.46523628927048575665e-4, -0.98374475304879564677e-4, 0.15808870322491248884e-3, -0.21026444172410488319e-3, 0.21743961811521264320e-3, -0.16431810653676389022e-3, 0.84418223983852743293e-4, -0.26190838401581408670e-4, 0.36899182659531622704e-5];

	// lgamma implementation ref: https://mrob.com/pub/ries/lanczos-gamma.html#code

	// log(2 * pi) / 2
	const lnSqrt2PI = probability.lnSqrt2PI = 0.91893853320467274178;
	const lgammaG = probability.lgammaG = 5; // Lanczos parameter "g"
	const lgammaN = probability.lgammaN = 7; // Range of coefficients "n"

	const lgammaSeries = probability.lgammaSeries = [1.000000000190015, 76.18009172947146, -86.50532032941677, 24.01409824083091, -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
	function lgammaNumber(n) {
	  if (n < 0) return NaN;
	  if (n === 0) return Infinity;
	  if (!Number.isFinite(n)) return n;
	  if (n < 0.5) {
	    // Use Euler's reflection formula:
	    // gamma(z) = PI / (sin(PI * z) * gamma(1 - z))
	    return Math.log(Math.PI / Math.sin(Math.PI * n)) - lgammaNumber(1 - n);
	  }

	  // Compute the logarithm of the Gamma function using the Lanczos method

	  n = n - 1;
	  const base = n + lgammaG + 0.5; // Base of the Lanczos exponential
	  let sum = lgammaSeries[0];

	  // We start with the terms that have the smallest coefficients and largest denominator
	  for (let i = lgammaN - 1; i >= 1; i--) {
	    sum += lgammaSeries[i] / (n + i);
	  }
	  return lnSqrt2PI + (n + 0.5) * Math.log(base) - base + Math.log(sum);
	}
	lgammaNumber.signature = 'number';
	return probability;
}

var trigonometry = {};

var hasRequiredTrigonometry;

function requireTrigonometry () {
	if (hasRequiredTrigonometry) return trigonometry;
	hasRequiredTrigonometry = 1;

	Object.defineProperty(trigonometry, "__esModule", {
	  value: true
	});
	trigonometry.acosNumber = acosNumber;
	trigonometry.acoshNumber = acoshNumber;
	trigonometry.acotNumber = acotNumber;
	trigonometry.acothNumber = acothNumber;
	trigonometry.acscNumber = acscNumber;
	trigonometry.acschNumber = acschNumber;
	trigonometry.asecNumber = asecNumber;
	trigonometry.asechNumber = asechNumber;
	trigonometry.asinNumber = asinNumber;
	trigonometry.asinhNumber = asinhNumber;
	trigonometry.atan2Number = atan2Number;
	trigonometry.atanNumber = atanNumber;
	trigonometry.atanhNumber = atanhNumber;
	trigonometry.cosNumber = cosNumber;
	trigonometry.coshNumber = coshNumber;
	trigonometry.cotNumber = cotNumber;
	trigonometry.cothNumber = cothNumber;
	trigonometry.cscNumber = cscNumber;
	trigonometry.cschNumber = cschNumber;
	trigonometry.secNumber = secNumber;
	trigonometry.sechNumber = sechNumber;
	trigonometry.sinNumber = sinNumber;
	trigonometry.sinhNumber = sinhNumber;
	trigonometry.tanNumber = tanNumber;
	trigonometry.tanhNumber = tanhNumber;
	var _number = requireNumber$3();
	const n1 = 'number';
	const n2 = 'number, number';
	function acosNumber(x) {
	  return Math.acos(x);
	}
	acosNumber.signature = n1;
	function acoshNumber(x) {
	  return (0, _number.acosh)(x);
	}
	acoshNumber.signature = n1;
	function acotNumber(x) {
	  return Math.atan(1 / x);
	}
	acotNumber.signature = n1;
	function acothNumber(x) {
	  return Number.isFinite(x) ? (Math.log((x + 1) / x) + Math.log(x / (x - 1))) / 2 : 0;
	}
	acothNumber.signature = n1;
	function acscNumber(x) {
	  return Math.asin(1 / x);
	}
	acscNumber.signature = n1;
	function acschNumber(x) {
	  const xInv = 1 / x;
	  return Math.log(xInv + Math.sqrt(xInv * xInv + 1));
	}
	acschNumber.signature = n1;
	function asecNumber(x) {
	  return Math.acos(1 / x);
	}
	asecNumber.signature = n1;
	function asechNumber(x) {
	  const xInv = 1 / x;
	  const ret = Math.sqrt(xInv * xInv - 1);
	  return Math.log(ret + xInv);
	}
	asechNumber.signature = n1;
	function asinNumber(x) {
	  return Math.asin(x);
	}
	asinNumber.signature = n1;
	function asinhNumber(x) {
	  return (0, _number.asinh)(x);
	}
	asinhNumber.signature = n1;
	function atanNumber(x) {
	  return Math.atan(x);
	}
	atanNumber.signature = n1;
	function atan2Number(y, x) {
	  return Math.atan2(y, x);
	}
	atan2Number.signature = n2;
	function atanhNumber(x) {
	  return (0, _number.atanh)(x);
	}
	atanhNumber.signature = n1;
	function cosNumber(x) {
	  return Math.cos(x);
	}
	cosNumber.signature = n1;
	function coshNumber(x) {
	  return (0, _number.cosh)(x);
	}
	coshNumber.signature = n1;
	function cotNumber(x) {
	  return 1 / Math.tan(x);
	}
	cotNumber.signature = n1;
	function cothNumber(x) {
	  const e = Math.exp(2 * x);
	  return (e + 1) / (e - 1);
	}
	cothNumber.signature = n1;
	function cscNumber(x) {
	  return 1 / Math.sin(x);
	}
	cscNumber.signature = n1;
	function cschNumber(x) {
	  // consider values close to zero (+/-)
	  if (x === 0) {
	    return Number.POSITIVE_INFINITY;
	  } else {
	    return Math.abs(2 / (Math.exp(x) - Math.exp(-x))) * (0, _number.sign)(x);
	  }
	}
	cschNumber.signature = n1;
	function secNumber(x) {
	  return 1 / Math.cos(x);
	}
	secNumber.signature = n1;
	function sechNumber(x) {
	  return 2 / (Math.exp(x) + Math.exp(-x));
	}
	sechNumber.signature = n1;
	function sinNumber(x) {
	  return Math.sin(x);
	}
	sinNumber.signature = n1;
	function sinhNumber(x) {
	  return (0, _number.sinh)(x);
	}
	sinhNumber.signature = n1;
	function tanNumber(x) {
	  return Math.tan(x);
	}
	tanNumber.signature = n1;
	function tanhNumber(x) {
	  return (0, _number.tanh)(x);
	}
	tanhNumber.signature = n1;
	return trigonometry;
}

var utils$1 = {};

var hasRequiredUtils$2;

function requireUtils$2 () {
	if (hasRequiredUtils$2) return utils$1;
	hasRequiredUtils$2 = 1;

	Object.defineProperty(utils$1, "__esModule", {
	  value: true
	});
	utils$1.isIntegerNumber = isIntegerNumber;
	utils$1.isNaNNumber = isNaNNumber;
	utils$1.isNegativeNumber = isNegativeNumber;
	utils$1.isPositiveNumber = isPositiveNumber;
	utils$1.isZeroNumber = isZeroNumber;
	var _number = requireNumber$3();
	const n1 = 'number';
	function isIntegerNumber(x) {
	  return (0, _number.isInteger)(x);
	}
	isIntegerNumber.signature = n1;
	function isNegativeNumber(x) {
	  return x < 0;
	}
	isNegativeNumber.signature = n1;
	function isPositiveNumber(x) {
	  return x > 0;
	}
	isPositiveNumber.signature = n1;
	function isZeroNumber(x) {
	  return x === 0;
	}
	isZeroNumber.signature = n1;
	function isNaNNumber(x) {
	  return Number.isNaN(x);
	}
	isNaNNumber.signature = n1;
	return utils$1;
}

var hasRequiredNumber$2;

function requireNumber$2 () {
	if (hasRequiredNumber$2) return number$2;
	hasRequiredNumber$2 = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		var _arithmetic = requireArithmetic();
		Object.keys(_arithmetic).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _arithmetic[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _arithmetic[key];
		    }
		  });
		});
		var _bitwise = requireBitwise$1();
		Object.keys(_bitwise).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _bitwise[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _bitwise[key];
		    }
		  });
		});
		var _combinations = requireCombinations$2();
		Object.keys(_combinations).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _combinations[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _combinations[key];
		    }
		  });
		});
		var _constants = requireConstants$4();
		Object.keys(_constants).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _constants[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _constants[key];
		    }
		  });
		});
		var _logical = requireLogical();
		Object.keys(_logical).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _logical[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _logical[key];
		    }
		  });
		});
		var _relational = requireRelational();
		Object.keys(_relational).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _relational[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _relational[key];
		    }
		  });
		});
		var _probability = requireProbability();
		Object.keys(_probability).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _probability[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _probability[key];
		    }
		  });
		});
		var _trigonometry = requireTrigonometry();
		Object.keys(_trigonometry).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _trigonometry[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _trigonometry[key];
		    }
		  });
		});
		var _utils = requireUtils$2();
		Object.keys(_utils).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _utils[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _utils[key];
		    }
		  });
		}); 
	} (number$2));
	return number$2;
}

var nearlyEqual = {};

var hasRequiredNearlyEqual;

function requireNearlyEqual () {
	if (hasRequiredNearlyEqual) return nearlyEqual;
	hasRequiredNearlyEqual = 1;

	Object.defineProperty(nearlyEqual, "__esModule", {
	  value: true
	});
	nearlyEqual.nearlyEqual = nearlyEqual$1;
	/**
	 * Compares two BigNumbers.
	 * @param {BigNumber} a - First value to compare
	 * @param {BigNumber} b - Second value to compare
	 * @param {number} [relTol=1e-09] - The relative tolerance, indicating the maximum allowed difference relative to the larger absolute value. Must be greater than 0.
	 * @param {number} [absTol=0] - The minimum absolute tolerance, useful for comparisons near zero. Must be at least 0.
	 * @returns {boolean} whether the two numbers are nearly equal
	 * @throws {Error} If `relTol` is less than or equal to 0.
	 * @throws {Error} If `absTol` is less than 0.
	 *
	 * @example
	 * nearlyEqual(1.000000001, 1.0, 1e-9);            // true
	 * nearlyEqual(1.000000002, 1.0, 0);            // false
	 * nearlyEqual(1.0, 1.009, undefined, 0.02);       // true
	 * nearlyEqual(0.000000001, 0.0, undefined, 1e-8); // true
	 */
	function nearlyEqual$1(a, b) {
	  let relTol = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1e-9;
	  let absTol = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
	  if (relTol <= 0) {
	    throw new Error('Relative tolerance must be greater than 0');
	  }
	  if (absTol < 0) {
	    throw new Error('Absolute tolerance must be at least 0');
	  }
	  // NaN
	  if (a.isNaN() || b.isNaN()) {
	    return false;
	  }
	  if (!a.isFinite() || !b.isFinite()) {
	    return a.eq(b);
	  }
	  // use "==" operator, handles infinities
	  if (a.eq(b)) {
	    return true;
	  }
	  // abs(a-b) <= max(relTol * max(abs(a), abs(b)), absTol)
	  return a.minus(b).abs().lte(a.constructor.max(a.constructor.max(a.abs(), b.abs()).mul(relTol), absTol));
	}
	return nearlyEqual;
}

var hasRequiredIsNegative$1;

function requireIsNegative$1 () {
	if (hasRequiredIsNegative$1) return isNegative$1;
	hasRequiredIsNegative$1 = 1;

	Object.defineProperty(isNegative$1, "__esModule", {
	  value: true
	});
	isNegative$1.createIsNegative = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	const name = 'isNegative';
	const dependencies = ['typed', 'config'];
	isNegative$1.createIsNegative = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config
	  } = _ref;
	  /**
	   * Test whether a value is negative: smaller than zero.
	   * The function supports types `number`, `BigNumber`, `Fraction`, and `Unit`.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isNegative(x)
	   *
	   * Examples:
	   *
	   *    math.isNegative(3)                     // returns false
	   *    math.isNegative(-2)                    // returns true
	   *    math.isNegative(0)                     // returns false
	   *    math.isNegative(-0)                    // returns false
	   *    math.isNegative(math.bignumber(2))     // returns false
	   *    math.isNegative(math.fraction(-2, 5))  // returns true
	   *    math.isNegative('-2')                  // returns true
	   *    math.isNegative([2, 0, -3])            // returns [false, false, true]
	   *
	   * See also:
	   *
	   *    isNumeric, isPositive, isZero, isInteger
	   *
	   * @param {number | BigNumber | bigint | Fraction | Unit | Array | Matrix} x  Value to be tested
	   * @return {boolean}  Returns true when `x` is larger than zero.
	   *                    Throws an error in case of an unknown data type.
	   */
	  return typed(name, {
	    number: x => (0, _number.nearlyEqual)(x, 0, config.relTol, config.absTol) ? false : (0, _index.isNegativeNumber)(x),
	    BigNumber: x => (0, _nearlyEqual.nearlyEqual)(x, new x.constructor(0), config.relTol, config.absTol) ? false : x.isNeg() && !x.isZero() && !x.isNaN(),
	    bigint: x => x < 0n,
	    Fraction: x => x.s < 0n,
	    // It's enough to decide on the sign

	    Unit: typed.referToSelf(self => x => typed.find(self, x.valueType())(x.value)),
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return isNegative$1;
}

var isNumeric$1 = {};

var hasRequiredIsNumeric$1;

function requireIsNumeric$1 () {
	if (hasRequiredIsNumeric$1) return isNumeric$1;
	hasRequiredIsNumeric$1 = 1;

	Object.defineProperty(isNumeric$1, "__esModule", {
	  value: true
	});
	isNumeric$1.createIsNumeric = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'isNumeric';
	const dependencies = ['typed'];
	isNumeric$1.createIsNumeric = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Test whether a value is an numeric value.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isNumeric(x)
	   *
	   * Examples:
	   *
	   *    math.isNumeric(2)                     // returns true
	   *    math.isNumeric('2')                   // returns false
	   *    math.hasNumericValue('2')             // returns true
	   *    math.isNumeric(0)                     // returns true
	   *    math.isNumeric(math.bignumber('42'))  // returns true
	   *    math.isNumeric(math.bigint('42'))     // returns true
	   *    math.isNumeric(math.fraction(4))      // returns true
	   *    math.isNumeric(math.complex('2-4i'))  // returns false
	   *    math.isNumeric([2.3, 'foo', false])   // returns [true, false, true]
	   *
	   * See also:
	   *
	   *    isZero, isPositive, isNegative, isInteger, hasNumericValue, isFinite, isBounded
	   *
	   * @param {*} x       Value to be tested
	   * @return {boolean}  Returns true when `x` is a `number`, `BigNumber`,
	   *                    `Fraction`, or `boolean`. Returns false for other types.
	   *                    Throws an error in case of unknown types.
	   */
	  return typed(name, {
	    'number | BigNumber | bigint | Fraction | boolean': () => true,
	    'Complex | Unit | string | null | undefined | Node': () => false,
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return isNumeric$1;
}

var hasNumericValue$1 = {};

var hasRequiredHasNumericValue$1;

function requireHasNumericValue$1 () {
	if (hasRequiredHasNumericValue$1) return hasNumericValue$1;
	hasRequiredHasNumericValue$1 = 1;

	Object.defineProperty(hasNumericValue$1, "__esModule", {
	  value: true
	});
	hasNumericValue$1.createHasNumericValue = void 0;
	var _factory = requireFactory$b();
	const name = 'hasNumericValue';
	const dependencies = ['typed', 'isNumeric'];
	hasNumericValue$1.createHasNumericValue = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isNumeric
	  } = _ref;
	  /**
	   * Test whether a value is an numeric value.
	   *
	   * In case of a string, true is returned if the string contains a numeric value.
	   *
	   * Syntax:
	   *
	   *     math.hasNumericValue(x)
	   *
	   * Examples:
	   *
	   *    math.hasNumericValue(2)                     // returns true
	   *    math.hasNumericValue('2')                   // returns true
	   *    math.isNumeric('2')                         // returns false
	   *    math.hasNumericValue(0)                     // returns true
	   *    math.hasNumericValue(math.bignumber('500')) // returns true
	   *    math.hasNumericValue(math.bigint('42'))     // returns true
	   *    math.hasNumericValue(42n)                   // returns true
	   *    math.hasNumericValue(math.fraction(4))      // returns true
	   *    math.hasNumericValue(math.complex('2-4i'))  // returns false
	   *    math.hasNumericValue(false)                 // returns true
	   *    math.hasNumericValue([2.3, 'foo', false])   // returns [true, false, true]
	   *
	   * See also:
	   *
	   *    isZero, isPositive, isNegative, isInteger, isNumeric
	   *
	   * @param {*} x       Value to be tested
	   * @return {boolean}  Returns true when `x` is a `number`, `BigNumber`,
	   *                    `Fraction`, `Boolean`, or a `String` containing number. Returns false for other types.
	   *                    Throws an error in case of unknown types.
	   */
	  return typed(name, {
	    boolean: () => true,
	    string: function (x) {
	      return x.trim().length > 0 && !isNaN(Number(x));
	    },
	    any: function (x) {
	      return isNumeric(x);
	    }
	  });
	});
	return hasNumericValue$1;
}

var isPositive$1 = {};

var hasRequiredIsPositive$1;

function requireIsPositive$1 () {
	if (hasRequiredIsPositive$1) return isPositive$1;
	hasRequiredIsPositive$1 = 1;

	Object.defineProperty(isPositive$1, "__esModule", {
	  value: true
	});
	isPositive$1.createIsPositive = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	const name = 'isPositive';
	const dependencies = ['typed', 'config'];
	isPositive$1.createIsPositive = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config
	  } = _ref;
	  /**
	   * Test whether a value is positive: larger than zero.
	   * The function supports types `number`, `BigNumber`, `Fraction`, and `Unit`.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isPositive(x)
	   *
	   * Examples:
	   *
	   *    math.isPositive(3)                     // returns true
	   *    math.isPositive(-2)                    // returns false
	   *    math.isPositive(0)                     // returns false
	   *    math.isPositive(-0)                    // returns false
	   *    math.isPositive(0.5)                   // returns true
	   *    math.isPositive(math.bignumber(2))     // returns true
	   *    math.isPositive(math.fraction(-2, 5))  // returns false
	   *    math.isPositive(math.fraction(1, 3))   // returns true
	   *    math.isPositive('2')                   // returns true
	   *    math.isPositive([2, 0, -3])            // returns [true, false, false]
	   *
	   * See also:
	   *
	   *    isNumeric, isZero, isNegative, isInteger
	   *
	   * @param {number | BigNumber | bigint | Fraction | Unit | Array | Matrix} x  Value to be tested
	   * @return {boolean}  Returns true when `x` is larger than zero.
	   *                    Throws an error in case of an unknown data type.
	   */
	  return typed(name, {
	    number: x => (0, _number.nearlyEqual)(x, 0, config.relTol, config.absTol) ? false : (0, _index.isPositiveNumber)(x),
	    BigNumber: x => (0, _nearlyEqual.nearlyEqual)(x, new x.constructor(0), config.relTol, config.absTol) ? false : !x.isNeg() && !x.isZero() && !x.isNaN(),
	    bigint: x => x > 0n,
	    Fraction: x => x.s > 0n && x.n > 0n,
	    Unit: typed.referToSelf(self => x => typed.find(self, x.valueType())(x.value)),
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return isPositive$1;
}

var isZero$1 = {};

var hasRequiredIsZero$1;

function requireIsZero$1 () {
	if (hasRequiredIsZero$1) return isZero$1;
	hasRequiredIsZero$1 = 1;

	Object.defineProperty(isZero$1, "__esModule", {
	  value: true
	});
	isZero$1.createIsZero = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'isZero';
	const dependencies = ['typed', 'equalScalar'];
	isZero$1.createIsZero = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Test whether a value is zero.
	   * The function can check for zero for types `number`, `BigNumber`, `Fraction`,
	   * `Complex`, and `Unit`.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isZero(x)
	   *
	   * Examples:
	   *
	   *    math.isZero(0)                      // returns true
	   *    math.isZero(2)                      // returns false
	   *    math.isZero(0.5)                    // returns false
	   *    math.isZero(math.bignumber(0))      // returns true
	   *    math.isZero(math.fraction(0))       // returns true
	   *    math.isZero(math.fraction(1,3))     // returns false
	   *    math.isZero(math.complex('2 - 4i')) // returns false
	   *    math.isZero(math.complex('0i'))     // returns true
	   *    math.isZero('0')                    // returns true
	   *    math.isZero('2')                    // returns false
	   *    math.isZero([2, 0, -3])             // returns [false, true, false]
	   *
	   * See also:
	   *
	   *    isNumeric, isPositive, isNegative, isInteger
	   *
	   * @param {number | BigNumber | bigint | Complex | Fraction | Unit | Array | Matrix} x       Value to be tested
	   * @return {boolean}  Returns true when `x` is zero.
	   *                    Throws an error in case of an unknown data type.
	   */
	  return typed(name, {
	    'number | BigNumber | Complex | Fraction': x => equalScalar(x, 0),
	    bigint: x => x === 0n,
	    Unit: typed.referToSelf(self => x => typed.find(self, x.valueType())(x.value)),
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return isZero$1;
}

var _isNaN$1 = {};

var hasRequired_isNaN$1;

function require_isNaN$1 () {
	if (hasRequired_isNaN$1) return _isNaN$1;
	hasRequired_isNaN$1 = 1;

	Object.defineProperty(_isNaN$1, "__esModule", {
	  value: true
	});
	_isNaN$1.createIsNaN = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'isNaN';
	const dependencies = ['typed'];
	_isNaN$1.createIsNaN = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Test whether a value is NaN (not a number).
	   * The function supports types `number`, `BigNumber`, `Fraction`, `Unit` and `Complex`.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isNaN(x)
	   *
	   * Examples:
	   *
	   *    math.isNaN(3)                     // returns false
	   *    math.isNaN(NaN)                   // returns true
	   *    math.isNaN(0)                     // returns false
	   *    math.isNaN(math.bignumber(NaN))   // returns true
	   *    math.isNaN(math.bignumber(0))     // returns false
	   *    math.isNaN(math.fraction(-2, 5))  // returns false
	   *    math.isNaN('-2')                  // returns false
	   *    math.isNaN([2, 0, -3, NaN])       // returns [false, false, false, true]
	   *
	   * See also:
	   *
	   *    isNumeric, isNegative, isPositive, isZero, isInteger, isFinite, isBounded
	   *
	   * @param {number | BigNumber | bigint | Fraction | Unit | Array | Matrix} x  Value to be tested
	   * @return {boolean}  Returns true when `x` is NaN.
	   *                    Throws an error in case of an unknown data type.
	   */
	  return typed(name, {
	    number: _index.isNaNNumber,
	    BigNumber: function (x) {
	      return x.isNaN();
	    },
	    bigint: function (x) {
	      return false;
	    },
	    Fraction: function (x) {
	      return false;
	    },
	    Complex: function (x) {
	      return x.isNaN();
	    },
	    Unit: function (x) {
	      return Number.isNaN(x.value);
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return _isNaN$1;
}

var isBounded$1 = {};

var hasRequiredIsBounded$1;

function requireIsBounded$1 () {
	if (hasRequiredIsBounded$1) return isBounded$1;
	hasRequiredIsBounded$1 = 1;

	Object.defineProperty(isBounded$1, "__esModule", {
	  value: true
	});
	isBounded$1.createIsBounded = void 0;
	var _factory = requireFactory$b();
	const name = 'isBounded';
	const dependencies = ['typed'];
	isBounded$1.createIsBounded = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Test whether a value is bounded. For scalars, this test is equivalent
	   * to the isFinite finiteness test. On the other hand, a Matrix or Array
	   * is defined to be bounded if every entry is finite.
	   *
	   * Syntax:
	   *
	   *     math.isBounded(x)
	   *
	   * Examples:
	   *
	   *    math.isBounded(0)                        // returns true
	   *    math.isBounded(NaN)                      // returns false
	   *    math.isBounded(math.bignumber(Infinity)) // returns false
	   *    math.isBounded(math.fraction(1,3))       // returns true
	   *    math.isBounded(math.complex('2 - 4i'))   // returns true
	   *    math.isBounded(-10000000000000000n)      // returns true
	   *    math.isBounded(undefined)                // returns false
	   *    math.isBounded(null)                     // returns false
	   *    math.isBounded([0.001, -3n, 0])          // returns true
	   *    math.isBounded([2, -Infinity, -3])       // returns false
	   *
	   * See also:
	   *
	   *    isFinite, isNumeric, isPositive, isNegative, isNaN
	   *
	   * @param {number | BigNumber | bigint | Complex | Fraction | Unit | Array | Matrix} x       Value to be tested
	   * @return {boolean}  Returns true when `x` is bounded.
	   */
	  return typed(name, {
	    number: n => Number.isFinite(n),
	    'BigNumber | Complex': x => x.isFinite(),
	    'bigint | Fraction': () => true,
	    'null | undefined': () => false,
	    Unit: typed.referToSelf(self => x => self(x.value)),
	    'Array | Matrix': typed.referToSelf(self => A => {
	      if (!Array.isArray(A)) A = A.valueOf();
	      return A.every(entry => self(entry));
	    })
	  });
	});
	return isBounded$1;
}

var _isFinite$1 = {};

var hasRequired_isFinite$1;

function require_isFinite$1 () {
	if (hasRequired_isFinite$1) return _isFinite$1;
	hasRequired_isFinite$1 = 1;

	Object.defineProperty(_isFinite$1, "__esModule", {
	  value: true
	});
	_isFinite$1.createIsFinite = void 0;
	var _factory = requireFactory$b();
	const name = 'isFinite';
	const dependencies = ['typed', 'isBounded', 'map'];
	_isFinite$1.createIsFinite = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isBounded,
	    map
	  } = _ref;
	  /**
	   * Test whether a value is finite.
	   *
	   * Operates elementwise on Array and Matrix values. To test if all entries
	   * of an Array or Matrix are finite, use isBounded.
	   *
	   * Syntax:
	   *
	   *     math.isFinite(x)
	   *
	   * Examples:
	   *
	   *    math.isFinite(0)                        // returns true
	   *    math.isFinite(NaN)                      // returns false
	   *    math.isFinite(math.bignumber(Infinity)) // returns false
	   *    math.isFinite(math.fraction(1,3))       // returns true
	   *    math.isFinite(math.complex('2 - 4i'))   // returns true
	   *    math.isFinite(-10000000000000000n)      // returns true
	   *    math.isFinite(undefined)                // returns false
	   *    math.isFinite(null)                     // returns false
	   *    math.isFinite([0.001, -3n, 0])          // Array [true, true, true]
	   *    math.isFinite([2, -Infinity, -3])       // Array [true, false, true]
	   *
	   * See also:
	   *
	   *    isBounded isNumeric, isPositive, isNegative, isNaN
	   *
	   * History:
	   *
	   *    v15.1   Created
	   *
	   * @param {number | BigNumber | bigint | Complex | Fraction | Unit | Array | Matrix} x
	   *     Value to be tested
	   * @return {boolean | Array | Matrix}
	   *     Whether the value, or its entries, are finite, as appropriate
	   *     to its type
	   */
	  return typed(name, {
	    'Array | Matrix': A => map(A, isBounded),
	    any: x => isBounded(x)
	  });
	});
	return _isFinite$1;
}

var typeOf$1 = {};

var hasRequiredTypeOf$1;

function requireTypeOf$1 () {
	if (hasRequiredTypeOf$1) return typeOf$1;
	hasRequiredTypeOf$1 = 1;

	Object.defineProperty(typeOf$1, "__esModule", {
	  value: true
	});
	typeOf$1.createTypeOf = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'typeOf';
	const dependencies = ['typed'];
	typeOf$1.createTypeOf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Determine the type of an entity.
	   *
	   * Syntax:
	   *
	   *    math.typeOf(x)
	   *
	   * Examples:
	   *
	   *    // This list is intended to include all relevant types, for testing
	   *    // purposes:
	   *    math.typeOf(3.5)                      // returns 'number'
	   *    math.typeOf(42n)                      // returns 'bigint'
	   *    math.typeOf(math.complex('2-4i'))     // returns 'Complex'
	   *    math.typeOf(math.unit('45 deg'))      // returns 'Unit'
	   *    math.typeOf('hello world')            // returns 'string'
	   *    math.typeOf(null)                     // returns 'null'
	   *    math.typeOf(true)                     // returns 'boolean'
	   *    math.typeOf([1, 2, 3])                // returns 'Array'
	   *    math.typeOf(new Date())               // returns 'Date'
	   *    math.typeOf(function () {})           // returns 'function'
	   *    math.typeOf({a: 2, b: 3})             // returns 'Object'
	   *    math.typeOf(/a regexp/)               // returns 'RegExp'
	   *    math.typeOf(undefined)                // returns 'undefined'
	   *    math.typeOf(math.bignumber('23e99'))  // returns 'BigNumber'
	   *    math.typeOf(math.chain(2))            // returns 'Chain'
	   *    math.typeOf(math.fraction(1, 3))      // returns 'Fraction'
	   *    math.typeOf(math.help('sqrt'))        // returns 'Help'
	   *    math.typeOf(math.index(1, 3))         // returns 'Index'
	   *    math.typeOf(math.matrix([[1],[3]]))   // returns 'DenseMatrix'
	   *    math.typeOf(math.matrix([],'sparse')) // returns 'SparseMatrix'
	   *    math.typeOf(new math.Range(0, 10))    // returns 'Range'
	   *    math.typeOf(math.evaluate('a=2\na'))  // returns 'ResultSet'
	   *    math.typeOf(math.parse('A[2]'))       // returns 'AccessorNode'
	   *    math.typeOf(math.parse('[1,2,3]'))    // returns 'ArrayNode'
	   *    math.typeOf(math.parse('x=2'))        // returns 'AssignmentNode'
	   *    math.typeOf(math.parse('a=2; b=3'))   // returns 'BlockNode'
	   *    math.typeOf(math.parse('x<0?-1:1'))   // returns 'ConditionalNode'
	   *    math.typeOf(math.parse('2.3'))        // returns 'ConstantNode'
	   *    math.typeOf(math.parse('f(x)=x^2'))   // returns 'FunctionAssignmentNode'
	   *    math.typeOf(math.parse('sqrt(4)'))    // returns 'FunctionNode'
	   *    math.typeOf(math.parse('A[2]').index) // returns 'IndexNode'
	   *    math.typeOf(math.parse('{a:2}'))      // returns 'ObjectNode'
	   *    math.typeOf(math.parse('(2+3)'))      // returns 'ParenthesisNode'
	   *    math.typeOf(math.parse('1:10'))       // returns 'RangeNode'
	   *    math.typeOf(math.parse('a<b<c'))      // returns 'RelationalNode'
	   *    math.typeOf(math.parse('x'))          // returns 'SymbolNode'
	   *
	   * @param {*} x     The variable for which to test the type.
	   * @return {string} Returns the name of the type. Primitive types are lower case,
	   *                  non-primitive types are upper-camel-case.
	   *                  For example 'number', 'string', 'Array', 'Date'.
	   */
	  return typed(name, {
	    any: _is.typeOf
	  });
	});
	return typeOf$1;
}

var equalScalar = {};

var complex$2 = {};

var hasRequiredComplex$2;

function requireComplex$2 () {
	if (hasRequiredComplex$2) return complex$2;
	hasRequiredComplex$2 = 1;

	Object.defineProperty(complex$2, "__esModule", {
	  value: true
	});
	complex$2.complexEquals = complexEquals;
	var _number = requireNumber$3();
	/**
	 * Test whether two complex values are equal provided a given relTol and absTol.
	 * Does not use or change the global Complex.EPSILON setting
	 * @param {Complex} x - The first complex number for comparison.
	 * @param {Complex} y - The second complex number for comparison.
	 * @param {number} relTol - The relative tolerance for comparison.
	 * @param {number} absTol - The absolute tolerance for comparison.
	 * @returns {boolean} - Returns true if the two complex numbers are equal within the given tolerances, otherwise returns false.
	 */
	function complexEquals(x, y, relTol, absTol) {
	  return (0, _number.nearlyEqual)(x.re, y.re, relTol, absTol) && (0, _number.nearlyEqual)(x.im, y.im, relTol, absTol);
	}
	return complex$2;
}

var compareUnits = {};

var hasRequiredCompareUnits;

function requireCompareUnits () {
	if (hasRequiredCompareUnits) return compareUnits;
	hasRequiredCompareUnits = 1;

	Object.defineProperty(compareUnits, "__esModule", {
	  value: true
	});
	compareUnits.createCompareUnits = void 0;
	var _factory = requireFactory$b();
	compareUnits.createCompareUnits = /* #__PURE__ */(0, _factory.factory)('compareUnits', ['typed'], _ref => {
	  let {
	    typed
	  } = _ref;
	  return {
	    'Unit, Unit': typed.referToSelf(self => (x, y) => {
	      if (!x.equalBase(y)) {
	        throw new Error('Cannot compare units with different base');
	      }
	      return typed.find(self, [x.valueType(), y.valueType()])(x.value, y.value);
	    })
	  };
	});
	return compareUnits;
}

var hasRequiredEqualScalar;

function requireEqualScalar () {
	if (hasRequiredEqualScalar) return equalScalar;
	hasRequiredEqualScalar = 1;

	Object.defineProperty(equalScalar, "__esModule", {
	  value: true
	});
	equalScalar.createEqualScalarNumber = equalScalar.createEqualScalar = void 0;
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _complex = requireComplex$2();
	var _compareUnits = requireCompareUnits();
	const name = 'equalScalar';
	const dependencies = ['typed', 'config'];
	equalScalar.createEqualScalar = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config
	  } = _ref;
	  const compareUnits = (0, _compareUnits.createCompareUnits)({
	    typed
	  });

	  /**
	   * Test whether two scalar values are nearly equal.
	   *
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Complex | Unit} x   First value to compare
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Complex} y          Second value to compare
	   * @return {boolean}                                                  Returns true when the compared values are equal, else returns false
	   * @private
	   */
	  return typed(name, {
	    'boolean, boolean': function (x, y) {
	      return x === y;
	    },
	    'number, number': function (x, y) {
	      return (0, _number.nearlyEqual)(x, y, config.relTol, config.absTol);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return x.eq(y) || (0, _nearlyEqual.nearlyEqual)(x, y, config.relTol, config.absTol);
	    },
	    'bigint, bigint': function (x, y) {
	      return x === y;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return x.equals(y);
	    },
	    'Complex, Complex': function (x, y) {
	      return (0, _complex.complexEquals)(x, y, config.relTol, config.absTol);
	    }
	  }, compareUnits);
	});
	equalScalar.createEqualScalarNumber = (0, _factory.factory)(name, ['typed', 'config'], _ref2 => {
	  let {
	    typed,
	    config
	  } = _ref2;
	  return typed(name, {
	    'number, number': function (x, y) {
	      return (0, _number.nearlyEqual)(x, y, config.relTol, config.absTol);
	    }
	  });
	});
	return equalScalar;
}

var SparseMatrix = {};

var hasRequiredSparseMatrix;

function requireSparseMatrix () {
	if (hasRequiredSparseMatrix) return SparseMatrix;
	hasRequiredSparseMatrix = 1;

	Object.defineProperty(SparseMatrix, "__esModule", {
	  value: true
	});
	SparseMatrix.createSparseMatrixClass = void 0;
	var _is = requireIs();
	var _number = requireNumber$3();
	var _string = requireString$2();
	var _object = requireObject$3();
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	var _optimizeCallback = requireOptimizeCallback();
	const name = 'SparseMatrix';
	const dependencies = ['typed', 'equalScalar', 'Matrix'];
	SparseMatrix.createSparseMatrixClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar,
	    Matrix
	  } = _ref;
	  /**
	   * Sparse Matrix implementation. This type (currently) implements 2D
	   * matrices only via the format known as
	   * [Compressed Column Storage](https://en.wikipedia.org/wiki/Sparse_matrix#Compressed_sparse_column_(CSC_or_CCS)).
	   *
	   * The structure/invariants of the internal data should be:
	   * 1. _values is an array of the nonzero values in order from top to bottom
	   *    (of each column), left to right.
	   * 2. _index is an array of row numbers, of the same length as and
	   *    corresponding positionally to _values.
	   * 3. _ptr is an array of length one more than the number of columns. For j
	   *    less than the number of columns, the "half-open" span of indices
	   *    _ptr[j] to _ptr[j+1] (i.e. including _ptr[j] if it is less than
	   *    _ptr[j+1], but never including _ptr[j+1]) are the indices in _values
	   *    of the nonzero elements in column j. Note there are no nonzero elements
	   *    in column j exactly when _ptr[j] === _ptr[j+1], and that the final
	   *    entry in _ptr is always exactly the number of nonzero entries in the
	   *    matrix.
	   * 4. _size is a length-2 array consisting of the number of rows followed by
	   *    the number of columns.
	   * 5. _datatype, if set, is the mathjs typeOf value of all entries of the
	   *    SparseMatrix.
	   * @class SparseMatrix
	   */
	  function SparseMatrix(data, datatype) {
	    if (!(this instanceof SparseMatrix)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    if (datatype && !(0, _is.isString)(datatype)) {
	      throw new Error('Invalid datatype: ' + datatype);
	    }
	    if ((0, _is.isMatrix)(data)) {
	      // create from matrix
	      _createFromMatrix(this, data, datatype);
	    } else if (data && (0, _is.isArray)(data.index) && (0, _is.isArray)(data.ptr) && (0, _is.isArray)(data.size)) {
	      // initialize fields
	      this._values = data.values;
	      this._index = data.index;
	      this._ptr = data.ptr;
	      this._size = data.size;
	      this._datatype = datatype || data.datatype;
	    } else if ((0, _is.isArray)(data)) {
	      // create from array
	      _createFromArray(this, data, datatype);
	    } else if (data) {
	      // unsupported type
	      throw new TypeError('Unsupported type of data (' + (0, _is.typeOf)(data) + ')');
	    } else {
	      // nothing provided
	      this._values = [];
	      this._index = [];
	      this._ptr = [0];
	      this._size = [0, 0];
	      this._datatype = datatype;
	    }
	  }
	  function _createFromMatrix(matrix, source, datatype) {
	    // check matrix type
	    if (source.type === 'SparseMatrix') {
	      // clone arrays
	      matrix._values = source._values ? (0, _object.clone)(source._values) : undefined;
	      matrix._index = (0, _object.clone)(source._index);
	      matrix._ptr = (0, _object.clone)(source._ptr);
	      matrix._size = (0, _object.clone)(source._size);
	      matrix._datatype = datatype || source._datatype;
	    } else {
	      // build from matrix data
	      _createFromArray(matrix, source.valueOf(), datatype || source._datatype);
	    }
	  }
	  function _createFromArray(matrix, data, datatype) {
	    // initialize fields
	    matrix._values = [];
	    matrix._index = [];
	    matrix._ptr = [];
	    matrix._datatype = datatype;
	    // discover rows & columns, do not use math.size() to avoid looping array twice
	    const rows = data.length;
	    let columns = 0;

	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    if ((0, _is.isString)(datatype)) {
	      // find signature that matches (datatype, datatype)
	      eq = typed.find(equalScalar, [datatype, datatype]) || equalScalar;
	      // convert 0 to the same datatype
	      zero = typed.convert(0, datatype);
	    }

	    // check we have rows (empty array)
	    if (rows > 0) {
	      // column index
	      let j = 0;
	      do {
	        // store pointer to values index
	        matrix._ptr.push(matrix._index.length);
	        // loop rows
	        for (let i = 0; i < rows; i++) {
	          // current row
	          const row = data[i];
	          // check row is an array
	          if ((0, _is.isArray)(row)) {
	            // update columns if needed (only on first column)
	            if (j === 0 && columns < row.length) {
	              columns = row.length;
	            }
	            // check row has column
	            if (j < row.length) {
	              // value
	              const v = row[j];
	              // check value != 0
	              if (!eq(v, zero)) {
	                // store value
	                matrix._values.push(v);
	                // index
	                matrix._index.push(i);
	              }
	            }
	          } else {
	            // update columns if needed (only on first column)
	            if (j === 0 && columns < 1) {
	              columns = 1;
	            }
	            // check value != 0 (row is a scalar)
	            if (!eq(row, zero)) {
	              // store value
	              matrix._values.push(row);
	              // index
	              matrix._index.push(i);
	            }
	          }
	        }
	        // increment index
	        j++;
	      } while (j < columns);
	    }
	    // store number of values in ptr
	    while (matrix._ptr.length <= columns) {
	      matrix._ptr.push(matrix._index.length);
	    }
	    // size
	    matrix._size = [rows, columns];
	  }
	  SparseMatrix.prototype = new Matrix();

	  /**
	   * Create a new SparseMatrix
	   */
	  SparseMatrix.prototype.createSparseMatrix = function (data, datatype) {
	    return new SparseMatrix(data, datatype);
	  };

	  /**
	   * Attach type information
	   */
	  Object.defineProperty(SparseMatrix, 'name', {
	    value: 'SparseMatrix'
	  });
	  SparseMatrix.prototype.constructor = SparseMatrix;
	  SparseMatrix.prototype.type = 'SparseMatrix';
	  SparseMatrix.prototype.isSparseMatrix = true;

	  /**
	   * Get the matrix type
	   *
	   * Usage:
	   *    const matrixType = matrix.getDataType()  // retrieves the matrix type
	   *
	   * @memberOf SparseMatrix
	   * @return {string}   type information; if multiple types are found from the Matrix, it will return "mixed"
	   */
	  SparseMatrix.prototype.getDataType = function () {
	    return (0, _array.getArrayDataType)(this._values, _is.typeOf);
	  };

	  /**
	   * Get the storage format used by the matrix.
	   *
	   * Usage:
	   *     const format = matrix.storage()   // retrieve storage format
	   *
	   * @memberof SparseMatrix
	   * @return {string}           The storage format.
	   */
	  SparseMatrix.prototype.storage = function () {
	    return 'sparse';
	  };

	  /**
	   * Get the datatype of the data stored in the matrix.
	   *
	   * Usage:
	   *     const format = matrix.datatype()    // retrieve matrix datatype
	   *
	   * @memberof SparseMatrix
	   * @return {string}           The datatype.
	   */
	  SparseMatrix.prototype.datatype = function () {
	    return this._datatype;
	  };

	  /**
	   * Create a new SparseMatrix
	   * @memberof SparseMatrix
	   * @param {Array} data
	   * @param {string} [datatype]
	   */
	  SparseMatrix.prototype.create = function (data, datatype) {
	    return new SparseMatrix(data, datatype);
	  };

	  /**
	   * Get the matrix density.
	   *
	   * Usage:
	   *     const density = matrix.density()                   // retrieve matrix density
	   *
	   * @memberof SparseMatrix
	   * @return {number}           The matrix density.
	   */
	  SparseMatrix.prototype.density = function () {
	    // rows & columns
	    const rows = this._size[0];
	    const columns = this._size[1];
	    // calculate density
	    return rows !== 0 && columns !== 0 ? this._index.length / (rows * columns) : 0;
	  };

	  /**
	   * Get a subset of the matrix, or replace a subset of the matrix.
	   *
	   * Usage:
	   *     const subset = matrix.subset(index)               // retrieve subset
	   *     const value = matrix.subset(index, replacement)   // replace subset
	   *
	   * @memberof SparseMatrix
	   * @param {Index} index
	   * @param {Array | Matrix | *} [replacement]
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be filled with zeros.
	   */
	  SparseMatrix.prototype.subset = function (index, replacement, defaultValue) {
	    // check it is a pattern matrix
	    if (!this._values) {
	      throw new Error('Cannot invoke subset on a Pattern only matrix');
	    }

	    // check arguments
	    switch (arguments.length) {
	      case 1:
	        return _getsubset(this, index);

	      // intentional fall through
	      case 2:
	      case 3:
	        return _setsubset(this, index, replacement, defaultValue);
	      default:
	        throw new SyntaxError('Wrong number of arguments');
	    }
	  };
	  function _getsubset(matrix, idx) {
	    // check idx
	    if (!(0, _is.isIndex)(idx)) {
	      throw new TypeError('Invalid index');
	    }
	    const isScalar = idx.isScalar();
	    if (isScalar) {
	      // return a scalar
	      return matrix.get(idx.min());
	    }
	    // validate dimensions
	    const size = idx.size();
	    if (size.length !== matrix._size.length) {
	      throw new _DimensionError.DimensionError(size.length, matrix._size.length);
	    }

	    // vars
	    let i, ii, k, kk;

	    // validate if any of the ranges in the index is out of range
	    const min = idx.min();
	    const max = idx.max();
	    for (i = 0, ii = matrix._size.length; i < ii; i++) {
	      (0, _array.validateIndex)(min[i], matrix._size[i]);
	      (0, _array.validateIndex)(max[i], matrix._size[i]);
	    }

	    // matrix arrays
	    const mvalues = matrix._values;
	    const mindex = matrix._index;
	    const mptr = matrix._ptr;

	    // rows & columns dimensions for result matrix
	    const rows = idx.dimension(0);
	    const columns = idx.dimension(1);

	    // workspace & permutation vector
	    const w = [];
	    const pv = [];

	    // loop rows in resulting matrix
	    function rowsCallback(i, r) {
	      // update permutation vector
	      pv[i] = r[0];
	      // mark i in workspace
	      w[i] = true;
	    }
	    if (Number.isInteger(rows)) rowsCallback(rows, [0]);else rows.forEach(rowsCallback);

	    // result matrix arrays
	    const values = mvalues ? [] : undefined;
	    const index = [];
	    const ptr = [];

	    // loop columns in result matrix
	    function columnsCallback(j) {
	      // update ptr
	      ptr.push(index.length);
	      // loop values in column j
	      for (k = mptr[j], kk = mptr[j + 1]; k < kk; k++) {
	        // row
	        i = mindex[k];
	        // check row is in result matrix
	        if (w[i] === true) {
	          // push index
	          index.push(pv[i]);
	          // check we need to process values
	          if (values) {
	            values.push(mvalues[k]);
	          }
	        }
	      }
	    }
	    if (Number.isInteger(columns)) columnsCallback(columns);else columns.forEach(columnsCallback);
	    // update ptr
	    ptr.push(index.length);

	    // return matrix
	    return new SparseMatrix({
	      values,
	      index,
	      ptr,
	      size,
	      datatype: matrix._datatype
	    });
	  }
	  function _setsubset(matrix, index, submatrix, defaultValue) {
	    // check index
	    if (!index || index.isIndex !== true) {
	      throw new TypeError('Invalid index');
	    }

	    // get index size and check whether the index contains a single value
	    const iSize = index.size();
	    const isScalar = index.isScalar();

	    // calculate the size of the submatrix, and convert it into an Array if needed
	    let sSize;
	    if ((0, _is.isMatrix)(submatrix)) {
	      // submatrix size
	      sSize = submatrix.size();
	      // use array representation
	      submatrix = submatrix.toArray();
	    } else {
	      // get submatrix size (array, scalar)
	      sSize = (0, _array.arraySize)(submatrix);
	    }

	    // check index is a scalar
	    if (isScalar) {
	      // verify submatrix is a scalar
	      if (sSize.length !== 0) {
	        throw new TypeError('Scalar expected');
	      }
	      // set value
	      matrix.set(index.min(), submatrix, defaultValue);
	    } else {
	      // validate dimensions, index size must be one or two dimensions
	      if (iSize.length !== 1 && iSize.length !== 2) {
	        throw new _DimensionError.DimensionError(iSize.length, matrix._size.length, '<');
	      }

	      // check submatrix and index have the same dimensions
	      if (sSize.length < iSize.length) {
	        // calculate number of missing outer dimensions
	        let i = 0;
	        let outer = 0;
	        while (iSize[i] === 1 && sSize[i] === 1) {
	          i++;
	        }
	        while (iSize[i] === 1) {
	          outer++;
	          i++;
	        }
	        // unsqueeze both outer and inner dimensions
	        submatrix = (0, _array.unsqueeze)(submatrix, iSize.length, outer, sSize);
	      }

	      // check whether the size of the submatrix matches the index size
	      if (!(0, _object.deepStrictEqual)(iSize, sSize)) {
	        throw new _DimensionError.DimensionError(iSize, sSize, '>');
	      }

	      // insert the sub matrix
	      if (iSize.length === 1) {
	        // if the replacement index only has 1 dimension, go trough each one and set its value
	        const range = index.dimension(0);
	        _forEachIndex(range, (dataIndex, subIndex) => {
	          (0, _array.validateIndex)(dataIndex);
	          matrix.set([dataIndex, 0], submatrix[subIndex[0]], defaultValue);
	        });
	      } else {
	        // if the replacement index has 2 dimensions, go through each one and set the value in the correct index
	        const firstDimensionRange = index.dimension(0);
	        const secondDimensionRange = index.dimension(1);
	        _forEachIndex(firstDimensionRange, (firstDataIndex, firstSubIndex) => {
	          (0, _array.validateIndex)(firstDataIndex);
	          _forEachIndex(secondDimensionRange, (secondDataIndex, secondSubIndex) => {
	            (0, _array.validateIndex)(secondDataIndex);
	            matrix.set([firstDataIndex, secondDataIndex], submatrix[firstSubIndex[0]][secondSubIndex[0]], defaultValue);
	          });
	        });
	      }
	    }
	    return matrix;
	    function _forEachIndex(index, callback) {
	      // iterate cases where index is a Matrix or a Number
	      if ((0, _is.isNumber)(index)) callback(index, [0]);else index.forEach(callback);
	    }
	  }

	  /**
	   * Get a single element from the matrix.
	   * @memberof SparseMatrix
	   * @param {number[]} index   Zero-based index
	   * @return {*} value
	   */
	  SparseMatrix.prototype.get = function (index) {
	    if (!(0, _is.isArray)(index)) {
	      throw new TypeError('Array expected');
	    }
	    if (index.length !== this._size.length) {
	      throw new _DimensionError.DimensionError(index.length, this._size.length);
	    }

	    // check it is a pattern matrix
	    if (!this._values) {
	      throw new Error('Cannot invoke get on a Pattern only matrix');
	    }

	    // row and column
	    const i = index[0];
	    const j = index[1];

	    // check i, j are valid
	    (0, _array.validateIndex)(i, this._size[0]);
	    (0, _array.validateIndex)(j, this._size[1]);

	    // find value index
	    const k = _getValueIndex(i, this._ptr[j], this._ptr[j + 1], this._index);
	    // check k is prior to next column k and it is in the correct row
	    if (k < this._ptr[j + 1] && this._index[k] === i) {
	      return this._values[k];
	    }
	    return 0;
	  };

	  /**
	   * Replace a single element in the matrix.
	   * @memberof SparseMatrix
	   * @param {number[]} index   Zero-based index
	   * @param {*} v
	   * @param {*} [defaultValue]        Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be set to zero.
	   * @return {SparseMatrix} self
	   */
	  SparseMatrix.prototype.set = function (index, v, defaultValue) {
	    if (!(0, _is.isArray)(index)) {
	      throw new TypeError('Array expected');
	    }
	    if (index.length !== this._size.length) {
	      throw new _DimensionError.DimensionError(index.length, this._size.length);
	    }

	    // check it is a pattern matrix
	    if (!this._values) {
	      throw new Error('Cannot invoke set on a Pattern only matrix');
	    }

	    // row and column
	    const i = index[0];
	    const j = index[1];

	    // rows & columns
	    let rows = this._size[0];
	    let columns = this._size[1];

	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    if ((0, _is.isString)(this._datatype)) {
	      // find signature that matches (datatype, datatype)
	      eq = typed.find(equalScalar, [this._datatype, this._datatype]) || equalScalar;
	      // convert 0 to the same datatype
	      zero = typed.convert(0, this._datatype);
	    }

	    // check we need to resize matrix
	    if (i > rows - 1 || j > columns - 1) {
	      // resize matrix
	      _resize(this, Math.max(i + 1, rows), Math.max(j + 1, columns), defaultValue);
	      // update rows & columns
	      rows = this._size[0];
	      columns = this._size[1];
	    }

	    // check i, j are valid
	    (0, _array.validateIndex)(i, rows);
	    (0, _array.validateIndex)(j, columns);

	    // find value index
	    const k = _getValueIndex(i, this._ptr[j], this._ptr[j + 1], this._index);
	    // check k is prior to next column k and it is in the correct row
	    if (k < this._ptr[j + 1] && this._index[k] === i) {
	      // check value != 0
	      if (!eq(v, zero)) {
	        // update value
	        this._values[k] = v;
	      } else {
	        // remove value from matrix
	        _remove(k, j, this._values, this._index, this._ptr);
	      }
	    } else {
	      if (!eq(v, zero)) {
	        // insert value @ (i, j)
	        _insert(k, i, j, v, this._values, this._index, this._ptr);
	      }
	    }
	    return this;
	  };
	  function _getValueIndex(i, top, bottom, index) {
	    // check row is on the bottom side
	    if (bottom - top === 0) {
	      return bottom;
	    }
	    // loop rows [top, bottom[
	    for (let r = top; r < bottom; r++) {
	      // check we found value index
	      if (index[r] === i) {
	        return r;
	      }
	    }
	    // we did not find row
	    return top;
	  }
	  function _remove(k, j, values, index, ptr) {
	    // remove value @ k
	    values.splice(k, 1);
	    index.splice(k, 1);
	    // update pointers
	    for (let x = j + 1; x < ptr.length; x++) {
	      ptr[x]--;
	    }
	  }
	  function _insert(k, i, j, v, values, index, ptr) {
	    // insert value
	    values.splice(k, 0, v);
	    // update row for k
	    index.splice(k, 0, i);
	    // update column pointers
	    for (let x = j + 1; x < ptr.length; x++) {
	      ptr[x]++;
	    }
	  }

	  /**
	   * Resize the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (resize in place).
	   *
	   * @memberof SparseMatrix
	   * @param {number[] | Matrix} size  The new size the matrix should have.
	   *                                  Since sparse matrices are always two-dimensional,
	   *                                  size must be two numbers in either an array or a matrix
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries.
	   *                                  If not provided, the matrix elements will
	   *                                  be filled with zeros.
	   * @param {boolean} [copy]          Return a resized copy of the matrix
	   *
	   * @return {Matrix}                 The resized matrix
	   */
	  SparseMatrix.prototype.resize = function (size, defaultValue, copy) {
	    // validate arguments
	    if (!(0, _is.isCollection)(size)) {
	      throw new TypeError('Array or Matrix expected');
	    }

	    // SparseMatrix input is always 2d, flatten this into 1d if it's indeed a vector
	    const sizeArray = size.valueOf().map(value => {
	      return Array.isArray(value) && value.length === 1 ? value[0] : value;
	    });
	    if (sizeArray.length !== 2) {
	      throw new Error('Only two dimensions matrix are supported');
	    }

	    // check sizes
	    sizeArray.forEach(function (value) {
	      if (!(0, _is.isNumber)(value) || !(0, _number.isInteger)(value) || value < 0) {
	        throw new TypeError('Invalid size, must contain positive integers ' + '(size: ' + (0, _string.format)(sizeArray) + ')');
	      }
	    });

	    // matrix to resize
	    const m = copy ? this.clone() : this;
	    // resize matrix
	    return _resize(m, sizeArray[0], sizeArray[1], defaultValue);
	  };
	  function _resize(matrix, rows, columns, defaultValue) {
	    // value to insert at the time of growing matrix
	    let value = defaultValue || 0;

	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    if ((0, _is.isString)(matrix._datatype)) {
	      // find signature that matches (datatype, datatype)
	      eq = typed.find(equalScalar, [matrix._datatype, matrix._datatype]) || equalScalar;
	      // convert 0 to the same datatype
	      zero = typed.convert(0, matrix._datatype);
	      // convert value to the same datatype
	      value = typed.convert(value, matrix._datatype);
	    }

	    // should we insert the value?
	    const ins = !eq(value, zero);

	    // old columns and rows
	    const r = matrix._size[0];
	    let c = matrix._size[1];
	    let i, j, k;

	    // check we need to increase columns
	    if (columns > c) {
	      // loop new columns
	      for (j = c; j < columns; j++) {
	        // update matrix._ptr for current column
	        matrix._ptr[j] = matrix._values.length;
	        // check we need to insert matrix._values
	        if (ins) {
	          // loop rows
	          for (i = 0; i < r; i++) {
	            // add new matrix._values
	            matrix._values.push(value);
	            // update matrix._index
	            matrix._index.push(i);
	          }
	        }
	      }
	      // store number of matrix._values in matrix._ptr
	      matrix._ptr[columns] = matrix._values.length;
	    } else if (columns < c) {
	      // truncate matrix._ptr
	      matrix._ptr.splice(columns + 1, c - columns);
	      // truncate matrix._values and matrix._index
	      matrix._values.splice(matrix._ptr[columns], matrix._values.length);
	      matrix._index.splice(matrix._ptr[columns], matrix._index.length);
	    }
	    // update columns
	    c = columns;

	    // check we need to increase rows
	    if (rows > r) {
	      // check we have to insert values
	      if (ins) {
	        // inserts
	        let n = 0;
	        // loop columns
	        for (j = 0; j < c; j++) {
	          // update matrix._ptr for current column
	          matrix._ptr[j] = matrix._ptr[j] + n;
	          // where to insert matrix._values
	          k = matrix._ptr[j + 1] + n;
	          // pointer
	          let p = 0;
	          // loop new rows, initialize pointer
	          for (i = r; i < rows; i++, p++) {
	            // add value
	            matrix._values.splice(k + p, 0, value);
	            // update matrix._index
	            matrix._index.splice(k + p, 0, i);
	            // increment inserts
	            n++;
	          }
	        }
	        // store number of matrix._values in matrix._ptr
	        matrix._ptr[c] = matrix._values.length;
	      }
	    } else if (rows < r) {
	      // deletes
	      let d = 0;
	      // loop columns
	      for (j = 0; j < c; j++) {
	        // update matrix._ptr for current column
	        matrix._ptr[j] = matrix._ptr[j] - d;
	        // where matrix._values start for next column
	        const k0 = matrix._ptr[j];
	        const k1 = matrix._ptr[j + 1] - d;
	        // loop matrix._index
	        for (k = k0; k < k1; k++) {
	          // row
	          i = matrix._index[k];
	          // check we need to delete value and matrix._index
	          if (i > rows - 1) {
	            // remove value
	            matrix._values.splice(k, 1);
	            // remove item from matrix._index
	            matrix._index.splice(k, 1);
	            // increase deletes
	            d++;
	          }
	        }
	      }
	      // update matrix._ptr for current column
	      matrix._ptr[j] = matrix._values.length;
	    }
	    // update matrix._size
	    matrix._size[0] = rows;
	    matrix._size[1] = columns;
	    // return matrix
	    return matrix;
	  }

	  /**
	   * Reshape the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (reshape in place).
	   *
	   * NOTE: This might be better suited to copy by default, instead of modifying
	   *       in place. For now, it operates in place to remain consistent with
	   *       resize().
	   *
	   * @memberof SparseMatrix
	   * @param {number[]} sizes          The new size the matrix should have.
	   *                                  Since sparse matrices are always two-dimensional,
	   *                                  size must be two numbers in either an array or a matrix
	   * @param {boolean} [copy]          Return a reshaped copy of the matrix
	   *
	   * @return {Matrix}                 The reshaped matrix
	   */
	  SparseMatrix.prototype.reshape = function (sizes, copy) {
	    // validate arguments
	    if (!(0, _is.isArray)(sizes)) {
	      throw new TypeError('Array expected');
	    }
	    if (sizes.length !== 2) {
	      throw new Error('Sparse matrices can only be reshaped in two dimensions');
	    }

	    // check sizes
	    sizes.forEach(function (value) {
	      if (!(0, _is.isNumber)(value) || !(0, _number.isInteger)(value) || value <= -2 || value === 0) {
	        throw new TypeError('Invalid size, must contain positive integers or -1 ' + '(size: ' + (0, _string.format)(sizes) + ')');
	      }
	    });
	    const currentLength = this._size[0] * this._size[1];
	    sizes = (0, _array.processSizesWildcard)(sizes, currentLength);
	    const newLength = sizes[0] * sizes[1];

	    // m * n must not change
	    if (currentLength !== newLength) {
	      throw new Error('Reshaping sparse matrix will result in the wrong number of elements');
	    }

	    // matrix to reshape
	    const m = copy ? this.clone() : this;

	    // return unchanged if the same shape
	    if (this._size[0] === sizes[0] && this._size[1] === sizes[1]) {
	      return m;
	    }

	    // Convert to COO format (generate a column index)
	    const colIndex = [];
	    for (let i = 0; i < m._ptr.length; i++) {
	      for (let j = 0; j < m._ptr[i + 1] - m._ptr[i]; j++) {
	        colIndex.push(i);
	      }
	    }

	    // Clone the values array
	    const values = m._values.slice();

	    // Clone the row index array
	    const rowIndex = m._index.slice();

	    // Transform the (row, column) indices
	    for (let i = 0; i < m._index.length; i++) {
	      const r1 = rowIndex[i];
	      const c1 = colIndex[i];
	      const flat = r1 * m._size[1] + c1;
	      colIndex[i] = flat % sizes[1];
	      rowIndex[i] = Math.floor(flat / sizes[1]);
	    }

	    // Now reshaping is supposed to preserve the row-major order, BUT these sparse matrices are stored
	    // in column-major order, so we have to reorder the value array now. One option is to use a multisort,
	    // sorting several arrays based on some other array.

	    // OR, we could easily just:

	    // 1. Remove all values from the matrix
	    m._values.length = 0;
	    m._index.length = 0;
	    m._ptr.length = sizes[1] + 1;
	    m._size = sizes.slice();
	    for (let i = 0; i < m._ptr.length; i++) {
	      m._ptr[i] = 0;
	    }

	    // 2. Re-insert all elements in the proper order (simplified code from SparseMatrix.prototype.set)
	    // This step is probably the most time-consuming
	    for (let h = 0; h < values.length; h++) {
	      const i = rowIndex[h];
	      const j = colIndex[h];
	      const v = values[h];
	      const k = _getValueIndex(i, m._ptr[j], m._ptr[j + 1], m._index);
	      _insert(k, i, j, v, m._values, m._index, m._ptr);
	    }

	    // The value indices are inserted out of order, but apparently that's... still OK?

	    return m;
	  };

	  /**
	   * Create a clone of the matrix
	   * @memberof SparseMatrix
	   * @return {SparseMatrix} clone
	   */
	  SparseMatrix.prototype.clone = function () {
	    const m = new SparseMatrix({
	      values: this._values ? (0, _object.clone)(this._values) : undefined,
	      index: (0, _object.clone)(this._index),
	      ptr: (0, _object.clone)(this._ptr),
	      size: (0, _object.clone)(this._size),
	      datatype: this._datatype
	    });
	    return m;
	  };

	  /**
	   * Retrieve the size of the matrix.
	   * @memberof SparseMatrix
	   * @returns {number[]} size
	   */
	  SparseMatrix.prototype.size = function () {
	    return this._size.slice(0); // copy the Array
	  };

	  /**
	   * Create a new matrix with the results of the callback function executed on
	   * each entry of the matrix.
	   * @memberof SparseMatrix
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   * @param {boolean} [skipZeros] Invoke callback function for non-zero values only.
	   *
	   * @return {SparseMatrix} matrix
	   */
	  SparseMatrix.prototype.map = function (callback, skipZeros) {
	    // check it is a pattern matrix
	    if (!this._values) {
	      throw new Error('Cannot invoke map on a Pattern only matrix');
	    }
	    // matrix instance
	    const me = this;
	    // rows and columns
	    const rows = this._size[0];
	    const columns = this._size[1];
	    const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, me, 'map');
	    // invoke callback
	    const invoke = function (v, i, j) {
	      // invoke callback
	      return fastCallback.fn(v, [i, j], me);
	    };
	    // invoke _map
	    return _map(this, 0, rows - 1, 0, columns - 1, invoke, skipZeros);
	  };

	  /**
	   * Create a new matrix with the results of the callback function executed on the interval
	   * [minRow..maxRow, minColumn..maxColumn].
	   */
	  function _map(matrix, minRow, maxRow, minColumn, maxColumn, callback, skipZeros) {
	    // result arrays
	    const values = [];
	    const index = [];
	    const ptr = [];

	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    if ((0, _is.isString)(matrix._datatype)) {
	      // find signature that matches (datatype, datatype)
	      eq = typed.find(equalScalar, [matrix._datatype, matrix._datatype]) || equalScalar;
	      // convert 0 to the same datatype
	      zero = typed.convert(0, matrix._datatype);
	    }

	    // invoke callback
	    const invoke = function (v, x, y) {
	      // invoke callback
	      const value = callback(v, x, y);
	      // check value != 0
	      if (!eq(value, zero)) {
	        // store value
	        values.push(value);
	        // index
	        index.push(x);
	      }
	    };
	    // loop columns
	    for (let j = minColumn; j <= maxColumn; j++) {
	      // store pointer to values index
	      ptr.push(values.length);
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = matrix._ptr[j];
	      const k1 = matrix._ptr[j + 1];
	      if (skipZeros) {
	        // loop k within [k0, k1[
	        for (let k = k0; k < k1; k++) {
	          // row index
	          const i = matrix._index[k];
	          // check i is in range
	          if (i >= minRow && i <= maxRow) {
	            // value @ k
	            invoke(matrix._values[k], i - minRow, j - minColumn);
	          }
	        }
	      } else {
	        // create a cache holding all defined values
	        const values = {};
	        for (let k = k0; k < k1; k++) {
	          const i = matrix._index[k];
	          values[i] = matrix._values[k];
	        }

	        // loop over all rows (indexes can be unordered so we can't use that),
	        // and either read the value or zero
	        for (let i = minRow; i <= maxRow; i++) {
	          const value = i in values ? values[i] : 0;
	          invoke(value, i - minRow, j - minColumn);
	        }
	      }
	    }

	    // store number of values in ptr
	    ptr.push(values.length);
	    // return sparse matrix
	    return new SparseMatrix({
	      values,
	      index,
	      ptr,
	      size: [maxRow - minRow + 1, maxColumn - minColumn + 1]
	    });
	  }

	  /**
	   * Execute a callback function on each entry of the matrix.
	   * @memberof SparseMatrix
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix being traversed.
	   * @param {boolean} [skipZeros] Invoke callback function for non-zero values only.
	   *                              If false, the indices are guaranteed to be in order,
	   *                              if true, the indices can be unordered.
	   */
	  SparseMatrix.prototype.forEach = function (callback, skipZeros) {
	    // check it is a pattern matrix
	    if (!this._values) {
	      throw new Error('Cannot invoke forEach on a Pattern only matrix');
	    }
	    // matrix instance
	    const me = this;
	    // rows and columns
	    const rows = this._size[0];
	    const columns = this._size[1];
	    const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, me, 'forEach');
	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = this._ptr[j];
	      const k1 = this._ptr[j + 1];
	      if (skipZeros) {
	        // loop k within [k0, k1[
	        for (let k = k0; k < k1; k++) {
	          // row index
	          const i = this._index[k];

	          // value @ k
	          // TODO apply a non indexed version of algorithm in case fastCallback is not optimized
	          fastCallback.fn(this._values[k], [i, j], me);
	        }
	      } else {
	        // create a cache holding all defined values
	        const values = {};
	        for (let k = k0; k < k1; k++) {
	          const i = this._index[k];
	          values[i] = this._values[k];
	        }

	        // loop over all rows (indexes can be unordered so we can't use that),
	        // and either read the value or zero
	        for (let i = 0; i < rows; i++) {
	          const value = i in values ? values[i] : 0;
	          fastCallback.fn(value, [i, j], me);
	        }
	      }
	    }
	  };

	  /**
	   * Iterate over the matrix elements, skipping zeros
	   * @return {Iterable<{ value, index: number[] }>}
	   */
	  SparseMatrix.prototype[Symbol.iterator] = function* () {
	    if (!this._values) {
	      throw new Error('Cannot iterate a Pattern only matrix');
	    }
	    const columns = this._size[1];
	    for (let j = 0; j < columns; j++) {
	      const k0 = this._ptr[j];
	      const k1 = this._ptr[j + 1];
	      for (let k = k0; k < k1; k++) {
	        // row index
	        const i = this._index[k];
	        yield {
	          value: this._values[k],
	          index: [i, j]
	        };
	      }
	    }
	  };

	  /**
	   * Create an Array with a copy of the data of the SparseMatrix
	   * @memberof SparseMatrix
	   * @returns {Array} array
	   */
	  SparseMatrix.prototype.toArray = function () {
	    return _toArray(this._values, this._index, this._ptr, this._size, true);
	  };

	  /**
	   * Get the primitive value of the SparseMatrix: a two dimensions array
	   * @memberof SparseMatrix
	   * @returns {Array} array
	   */
	  SparseMatrix.prototype.valueOf = function () {
	    return _toArray(this._values, this._index, this._ptr, this._size, false);
	  };
	  function _toArray(values, index, ptr, size, copy) {
	    // rows and columns
	    const rows = size[0];
	    const columns = size[1];
	    // result
	    const a = [];
	    // vars
	    let i, j;
	    // initialize array
	    for (i = 0; i < rows; i++) {
	      a[i] = [];
	      for (j = 0; j < columns; j++) {
	        a[i][j] = 0;
	      }
	    }

	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = ptr[j];
	      const k1 = ptr[j + 1];
	      // loop k within [k0, k1[
	      for (let k = k0; k < k1; k++) {
	        // row index
	        i = index[k];
	        // set value (use one for pattern matrix)
	        a[i][j] = values ? copy ? (0, _object.clone)(values[k]) : values[k] : 1;
	      }
	    }
	    return a;
	  }

	  /**
	   * Get a string representation of the matrix, with optional formatting options.
	   * @memberof SparseMatrix
	   * @param {Object | number | Function} [options]  Formatting options. See
	   *                                                lib/utils/number:format for a
	   *                                                description of the available
	   *                                                options.
	   * @returns {string} str
	   */
	  SparseMatrix.prototype.format = function (options) {
	    // rows and columns
	    const rows = this._size[0];
	    const columns = this._size[1];
	    // density
	    const density = this.density();
	    // rows & columns
	    let str = 'Sparse Matrix [' + (0, _string.format)(rows, options) + ' x ' + (0, _string.format)(columns, options) + '] density: ' + (0, _string.format)(density, options) + '\n';
	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = this._ptr[j];
	      const k1 = this._ptr[j + 1];
	      // loop k within [k0, k1[
	      for (let k = k0; k < k1; k++) {
	        // row index
	        const i = this._index[k];
	        // append value
	        str += '\n    (' + (0, _string.format)(i, options) + ', ' + (0, _string.format)(j, options) + ') ==> ' + (this._values ? (0, _string.format)(this._values[k], options) : 'X');
	      }
	    }
	    return str;
	  };

	  /**
	   * Get a string representation of the matrix
	   * @memberof SparseMatrix
	   * @returns {string} str
	   */
	  SparseMatrix.prototype.toString = function () {
	    return (0, _string.format)(this.toArray());
	  };

	  /**
	   * Get a JSON representation of the matrix
	   * @memberof SparseMatrix
	   * @returns {Object}
	   */
	  SparseMatrix.prototype.toJSON = function () {
	    return {
	      mathjs: 'SparseMatrix',
	      values: this._values,
	      index: this._index,
	      ptr: this._ptr,
	      size: this._size,
	      datatype: this._datatype
	    };
	  };

	  /**
	   * Get the kth Matrix diagonal.
	   *
	   * @memberof SparseMatrix
	   * @param {number | BigNumber} [k=0]     The kth diagonal where the vector will retrieved.
	   *
	   * @returns {Matrix}                     The matrix vector with the diagonal values.
	   */
	  SparseMatrix.prototype.diagonal = function (k) {
	    // validate k if any
	    if (k) {
	      // convert BigNumber to a number
	      if ((0, _is.isBigNumber)(k)) {
	        k = k.toNumber();
	      }
	      // is must be an integer
	      if (!(0, _is.isNumber)(k) || !(0, _number.isInteger)(k)) {
	        throw new TypeError('The parameter k must be an integer number');
	      }
	    } else {
	      // default value
	      k = 0;
	    }
	    const kSuper = k > 0 ? k : 0;
	    const kSub = k < 0 ? -k : 0;

	    // rows & columns
	    const rows = this._size[0];
	    const columns = this._size[1];

	    // number diagonal values
	    const n = Math.min(rows - kSub, columns - kSuper);

	    // diagonal arrays
	    const values = [];
	    const index = [];
	    const ptr = [];
	    // initial ptr value
	    ptr[0] = 0;
	    // loop columns
	    for (let j = kSuper; j < columns && values.length < n; j++) {
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = this._ptr[j];
	      const k1 = this._ptr[j + 1];
	      // loop x within [k0, k1[
	      for (let x = k0; x < k1; x++) {
	        // row index
	        const i = this._index[x];
	        // check row
	        if (i === j - kSuper + kSub) {
	          // value on this column
	          values.push(this._values[x]);
	          // store row
	          index[values.length - 1] = i - kSub;
	          // exit loop
	          break;
	        }
	      }
	    }
	    // close ptr
	    ptr.push(values.length);
	    // return matrix
	    return new SparseMatrix({
	      values,
	      index,
	      ptr,
	      size: [n, 1]
	    });
	  };

	  /**
	   * Generate a matrix from a JSON object
	   * @memberof SparseMatrix
	   * @param {Object} json  An object structured like
	   *                       `{"mathjs": "SparseMatrix", "values": [], "index": [], "ptr": [], "size": []}`,
	   *                       where mathjs is optional
	   * @returns {SparseMatrix}
	   */
	  SparseMatrix.fromJSON = function (json) {
	    return new SparseMatrix(json);
	  };

	  /**
	   * Create a diagonal matrix.
	   *
	   * @memberof SparseMatrix
	   * @param {Array} size                       The matrix size.
	   * @param {number | Array | Matrix } value   The values for the diagonal.
	   * @param {number | BigNumber} [k=0]         The kth diagonal where the vector will be filled in.
	   * @param {number} [defaultValue]            The default value for non-diagonal
	   * @param {string} [datatype]                The Matrix datatype, values must be of this datatype.
	   *
	   * @returns {SparseMatrix}
	   */
	  SparseMatrix.diagonal = function (size, value, k, defaultValue, datatype) {
	    if (!(0, _is.isArray)(size)) {
	      throw new TypeError('Array expected, size parameter');
	    }
	    if (size.length !== 2) {
	      throw new Error('Only two dimensions matrix are supported');
	    }

	    // map size & validate
	    size = size.map(function (s) {
	      // check it is a big number
	      if ((0, _is.isBigNumber)(s)) {
	        // convert it
	        s = s.toNumber();
	      }
	      // validate arguments
	      if (!(0, _is.isNumber)(s) || !(0, _number.isInteger)(s) || s < 1) {
	        throw new Error('Size values must be positive integers');
	      }
	      return s;
	    });

	    // validate k if any
	    if (k) {
	      // convert BigNumber to a number
	      if ((0, _is.isBigNumber)(k)) {
	        k = k.toNumber();
	      }
	      // is must be an integer
	      if (!(0, _is.isNumber)(k) || !(0, _number.isInteger)(k)) {
	        throw new TypeError('The parameter k must be an integer number');
	      }
	    } else {
	      // default value
	      k = 0;
	    }

	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    if ((0, _is.isString)(datatype)) {
	      // find signature that matches (datatype, datatype)
	      eq = typed.find(equalScalar, [datatype, datatype]) || equalScalar;
	      // convert 0 to the same datatype
	      zero = typed.convert(0, datatype);
	    }
	    const kSuper = k > 0 ? k : 0;
	    const kSub = k < 0 ? -k : 0;

	    // rows and columns
	    const rows = size[0];
	    const columns = size[1];

	    // number of non-zero items
	    const n = Math.min(rows - kSub, columns - kSuper);

	    // value extraction function
	    let _value;

	    // check value
	    if ((0, _is.isArray)(value)) {
	      // validate array
	      if (value.length !== n) {
	        // number of values in array must be n
	        throw new Error('Invalid value array length');
	      }
	      // define function
	      _value = function (i) {
	        // return value @ i
	        return value[i];
	      };
	    } else if ((0, _is.isMatrix)(value)) {
	      // matrix size
	      const ms = value.size();
	      // validate matrix
	      if (ms.length !== 1 || ms[0] !== n) {
	        // number of values in array must be n
	        throw new Error('Invalid matrix length');
	      }
	      // define function
	      _value = function (i) {
	        // return value @ i
	        return value.get([i]);
	      };
	    } else {
	      // define function
	      _value = function () {
	        // return value
	        return value;
	      };
	    }

	    // create arrays
	    const values = [];
	    const index = [];
	    const ptr = [];

	    // loop items
	    for (let j = 0; j < columns; j++) {
	      // number of rows with value
	      ptr.push(values.length);
	      // diagonal index
	      const i = j - kSuper;
	      // check we need to set diagonal value
	      if (i >= 0 && i < n) {
	        // get value @ i
	        const v = _value(i);
	        // check for zero
	        if (!eq(v, zero)) {
	          // column
	          index.push(i + kSub);
	          // add value
	          values.push(v);
	        }
	      }
	    }
	    // last value should be number of values
	    ptr.push(values.length);
	    // create SparseMatrix
	    return new SparseMatrix({
	      values,
	      index,
	      ptr,
	      size: [rows, columns]
	    });
	  };

	  /**
	   * Swap rows i and j in Matrix.
	   *
	   * @memberof SparseMatrix
	   * @param {number} i       Matrix row index 1
	   * @param {number} j       Matrix row index 2
	   *
	   * @return {Matrix}        The matrix reference
	   */
	  SparseMatrix.prototype.swapRows = function (i, j) {
	    // check index
	    if (!(0, _is.isNumber)(i) || !(0, _number.isInteger)(i) || !(0, _is.isNumber)(j) || !(0, _number.isInteger)(j)) {
	      throw new Error('Row index must be positive integers');
	    }
	    // check dimensions
	    if (this._size.length !== 2) {
	      throw new Error('Only two dimensional matrix is supported');
	    }
	    // validate index
	    (0, _array.validateIndex)(i, this._size[0]);
	    (0, _array.validateIndex)(j, this._size[0]);

	    // swap rows
	    SparseMatrix._swapRows(i, j, this._size[1], this._values, this._index, this._ptr);
	    // return current instance
	    return this;
	  };

	  /**
	   * Loop rows with data in column j.
	   *
	   * @param {number} j            Column
	   * @param {Array} values        Matrix values
	   * @param {Array} index         Matrix row indeces
	   * @param {Array} ptr           Matrix column pointers
	   * @param {Function} callback   Callback function invoked for every row in column j
	   */
	  SparseMatrix._forEachRow = function (j, values, index, ptr, callback) {
	    // indeces for column j
	    const k0 = ptr[j];
	    const k1 = ptr[j + 1];

	    // loop
	    for (let k = k0; k < k1; k++) {
	      // invoke callback
	      callback(index[k], values[k]);
	    }
	  };

	  /**
	   * Swap rows x and y in Sparse Matrix data structures.
	   *
	   * @param {number} x         Matrix row index 1
	   * @param {number} y         Matrix row index 2
	   * @param {number} columns   Number of columns in matrix
	   * @param {Array} values     Matrix values
	   * @param {Array} index      Matrix row indeces
	   * @param {Array} ptr        Matrix column pointers
	   */
	  SparseMatrix._swapRows = function (x, y, columns, values, index, ptr) {
	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = ptr[j];
	      const k1 = ptr[j + 1];
	      // find value index @ x
	      const kx = _getValueIndex(x, k0, k1, index);
	      // find value index @ x
	      const ky = _getValueIndex(y, k0, k1, index);
	      // check both rows exist in matrix
	      if (kx < k1 && ky < k1 && index[kx] === x && index[ky] === y) {
	        // swap values (check for pattern matrix)
	        if (values) {
	          const v = values[kx];
	          values[kx] = values[ky];
	          values[ky] = v;
	        }
	        // next column
	        continue;
	      }
	      // check x row exist & no y row
	      if (kx < k1 && index[kx] === x && (ky >= k1 || index[ky] !== y)) {
	        // value @ x (check for pattern matrix)
	        const vx = values ? values[kx] : undefined;
	        // insert value @ y
	        index.splice(ky, 0, y);
	        if (values) {
	          values.splice(ky, 0, vx);
	        }
	        // remove value @ x (adjust array index if needed)
	        index.splice(ky <= kx ? kx + 1 : kx, 1);
	        if (values) {
	          values.splice(ky <= kx ? kx + 1 : kx, 1);
	        }
	        // next column
	        continue;
	      }
	      // check y row exist & no x row
	      if (ky < k1 && index[ky] === y && (kx >= k1 || index[kx] !== x)) {
	        // value @ y (check for pattern matrix)
	        const vy = values ? values[ky] : undefined;
	        // insert value @ x
	        index.splice(kx, 0, x);
	        if (values) {
	          values.splice(kx, 0, vy);
	        }
	        // remove value @ y (adjust array index if needed)
	        index.splice(kx <= ky ? ky + 1 : ky, 1);
	        if (values) {
	          values.splice(kx <= ky ? ky + 1 : ky, 1);
	        }
	      }
	    }
	  };
	  return SparseMatrix;
	}, {
	  isClass: true
	});
	return SparseMatrix;
}

var number$1 = {};

var hasRequiredNumber$1;

function requireNumber$1 () {
	if (hasRequiredNumber$1) return number$1;
	hasRequiredNumber$1 = 1;

	Object.defineProperty(number$1, "__esModule", {
	  value: true
	});
	number$1.createNumber = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'number';
	const dependencies = ['typed'];

	/**
	 * Separates the radix, integer part, and fractional part of a non decimal number string
	 * @param {string} input string to parse
	 * @returns {object} the parts of the string or null if not a valid input
	 */
	function getNonDecimalNumberParts(input) {
	  const nonDecimalWithRadixMatch = input.match(/(0[box])([0-9a-fA-F]*)\.([0-9a-fA-F]*)/);
	  if (nonDecimalWithRadixMatch) {
	    const radix = {
	      '0b': 2,
	      '0o': 8,
	      '0x': 16
	    }[nonDecimalWithRadixMatch[1]];
	    const integerPart = nonDecimalWithRadixMatch[2];
	    const fractionalPart = nonDecimalWithRadixMatch[3];
	    return {
	      input,
	      radix,
	      integerPart,
	      fractionalPart
	    };
	  } else {
	    return null;
	  }
	}

	/**
	 * Makes a number from a radix, and integer part, and a fractional part
	 * @param {parts} [x] parts of the number string (from getNonDecimalNumberParts)
	 * @returns {number} the number
	 */
	function makeNumberFromNonDecimalParts(parts) {
	  const n = parseInt(parts.integerPart, parts.radix);
	  let f = 0;
	  for (let i = 0; i < parts.fractionalPart.length; i++) {
	    const digitValue = parseInt(parts.fractionalPart[i], parts.radix);
	    f += digitValue / Math.pow(parts.radix, i + 1);
	  }
	  const result = n + f;
	  if (isNaN(result)) {
	    throw new SyntaxError('String "' + parts.input + '" is not a valid number');
	  }
	  return result;
	}
	number$1.createNumber = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Create a number or convert a string, boolean, or unit to a number.
	   * When value is a matrix, all elements will be converted to number.
	   *
	   * Syntax:
	   *
	   *    math.number(value)
	   *    math.number(unit, valuelessUnit)
	   *
	   * Examples:
	   *
	   *    math.number(2)                         // returns number 2
	   *    math.number('7.2')                     // returns number 7.2
	   *    math.number(true)                      // returns number 1
	   *    math.number([true, false, true, true]) // returns [1, 0, 1, 1]
	   *    math.number(math.unit('52cm'), 'm')    // returns 0.52
	   *
	   * See also:
	   *
	   *    bignumber, bigint, boolean, numeric, complex, index, matrix, string, unit
	   *
	   * @param {string | number | BigNumber | Fraction | boolean | Array | Matrix | Unit | null} [value]  Value to be converted
	   * @param {Unit | string} [valuelessUnit] A valueless unit, used to convert a unit to a number
	   * @return {number | Array | Matrix} The created number
	   */
	  const number = typed('number', {
	    '': function () {
	      return 0;
	    },
	    number: function (x) {
	      return x;
	    },
	    string: function (x) {
	      if (x === 'NaN') return NaN;
	      const nonDecimalNumberParts = getNonDecimalNumberParts(x);
	      if (nonDecimalNumberParts) {
	        return makeNumberFromNonDecimalParts(nonDecimalNumberParts);
	      }
	      let size = 0;
	      const wordSizeSuffixMatch = x.match(/(0[box][0-9a-fA-F]*)i([0-9]*)/);
	      if (wordSizeSuffixMatch) {
	        // x includes a size suffix like 0xffffi32, so we extract
	        // the suffix and remove it from x
	        size = Number(wordSizeSuffixMatch[2]);
	        x = wordSizeSuffixMatch[1];
	      }
	      let num = Number(x);
	      if (isNaN(num)) {
	        throw new SyntaxError('String "' + x + '" is not a valid number');
	      }
	      if (wordSizeSuffixMatch) {
	        // x is a signed bin, oct, or hex literal
	        // num is the value of string x if x is interpreted as unsigned
	        if (num > 2 ** size - 1) {
	          // literal is too large for size suffix
	          throw new SyntaxError(`String "${x}" is out of range`);
	        }
	        // check if the bit at index size - 1 is set and if so do the twos complement
	        if (num >= 2 ** (size - 1)) {
	          num = num - 2 ** size;
	        }
	      }
	      return num;
	    },
	    BigNumber: function (x) {
	      return x.toNumber();
	    },
	    bigint: function (x) {
	      return Number(x);
	    },
	    Fraction: function (x) {
	      return x.valueOf();
	    },
	    Unit: typed.referToSelf(self => x => {
	      const clone = x.clone();
	      clone.value = self(x.value);
	      return clone;
	    }),
	    null: function (x) {
	      return 0;
	    },
	    'Unit, string | Unit': function (unit, valuelessUnit) {
	      return unit.toNumber(valuelessUnit);
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });

	  // reviver function to parse a JSON object like:
	  //
	  //     {"mathjs":"number","value":"2.3"}
	  //
	  // into a number 2.3
	  number.fromJSON = function (json) {
	    return parseFloat(json.value);
	  };
	  return number;
	});
	return number$1;
}

var bigint$2 = {};

var hasRequiredBigint$2;

function requireBigint$2 () {
	if (hasRequiredBigint$2) return bigint$2;
	hasRequiredBigint$2 = 1;

	Object.defineProperty(bigint$2, "__esModule", {
	  value: true
	});
	bigint$2.createBigint = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'bigint';
	const dependencies = ['typed'];
	bigint$2.createBigint = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Create a bigint or convert a string, boolean, or unit to a bigint.
	   * When value is a matrix, all elements will be converted to bigint.
	   *
	   * Syntax:
	   *
	   *    math.bigint(value)
	   *
	   * Examples:
	   *
	   *    math.bigint(2)                         // returns 2n
	   *    math.bigint('123')                     // returns 123n
	   *    math.bigint(true)                      // returns 1n
	   *    math.bigint([true, false, true, true]) // returns [1n, 0n, 1n, 1n]
	   *
	   * See also:
	   *
	   *    number, bignumber, boolean, complex, index, matrix, string, unit
	   *
	   * History:
	   *
	   *    v13      Created
	   *    v14.2.1  Added conversion options
	   *
	   * @param {string | number | BigNumber | bigint | Fraction | boolean | Array | Matrix | null} [value]  Value to be converted
	   * @return {bigint | Array | Matrix} The created bigint
	   */
	  const bigint = typed('bigint', {
	    '': function () {
	      return 0n;
	    },
	    bigint: function (x) {
	      return x;
	    },
	    number: function (x) {
	      return BigInt(x.toFixed());
	    },
	    BigNumber: function (x) {
	      return BigInt(x.round().toString());
	    },
	    Fraction: function (x) {
	      return BigInt(x.valueOf().toFixed());
	    },
	    'string | boolean': function (x) {
	      return BigInt(x);
	    },
	    null: function (x) {
	      return 0n;
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });

	  // reviver function to parse a JSON object like:
	  //
	  //     {"mathjs":"bigint","value":"123"}
	  //
	  // into a bigint 123n
	  bigint.fromJSON = function (json) {
	    return BigInt(json.value);
	  };
	  return bigint;
	});
	return bigint$2;
}

var string$1 = {};

var hasRequiredString$1;

function requireString$1 () {
	if (hasRequiredString$1) return string$1;
	hasRequiredString$1 = 1;

	Object.defineProperty(string$1, "__esModule", {
	  value: true
	});
	string$1.createString = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _number = requireNumber$3();
	const name = 'string';
	const dependencies = ['typed'];
	string$1.createString = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Create a string or convert any object into a string.
	   * Elements of Arrays and Matrices are processed element wise.
	   *
	   * Syntax:
	   *
	   *    math.string(value)
	   *
	   * Examples:
	   *
	   *    math.string(4.2)                // returns string '4.2'
	   *    math.string(math.complex(3, 2)) // returns string '3 + 2i'
	   *
	   *    const u = math.unit(5, 'km')
	   *    math.string(u.to('m'))          // returns string '5000 m'
	   *
	   *    math.string([true, false])      // returns ['true', 'false']
	   *
	   * See also:
	   *
	   *    bignumber, boolean, complex, index, matrix, number, unit
	   *
	   * History:
	   *
	   *    v0.9  Created
	   *
	   * @param {* | Array | Matrix | null} [value]  A value to convert to a string
	   * @return {string | Array | Matrix} The created string
	   */
	  return typed(name, {
	    '': function () {
	      return '';
	    },
	    number: _number.format,
	    null: function (x) {
	      return 'null';
	    },
	    boolean: function (x) {
	      return x + '';
	    },
	    string: function (x) {
	      return x;
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self)),
	    any: function (x) {
	      return String(x);
	    }
	  });
	});
	return string$1;
}

var boolean$1 = {};

var hasRequiredBoolean$1;

function requireBoolean$1 () {
	if (hasRequiredBoolean$1) return boolean$1;
	hasRequiredBoolean$1 = 1;

	Object.defineProperty(boolean$1, "__esModule", {
	  value: true
	});
	boolean$1.createBoolean = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'boolean';
	const dependencies = ['typed'];
	boolean$1.createBoolean = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Create a boolean or convert a string or number to a boolean.
	   * In case of a number, `true` is returned for non-zero numbers, and `false` in
	   * case of zero.
	   * Strings can be `'true'` or `'false'`, or can contain a number.
	   * When value is a matrix, all elements will be converted to boolean.
	   *
	   * Syntax:
	   *
	   *    math.boolean(x)
	   *
	   * Examples:
	   *
	   *    math.boolean(0)     // returns false
	   *    math.boolean(1)     // returns true
	   *    math.boolean(-3)     // returns true
	   *    math.boolean('true')     // returns true
	   *    math.boolean('false')     // returns false
	   *    math.boolean([1, 0, 1, 1])     // returns [true, false, true, true]
	   *
	   * See also:
	   *
	   *    bignumber, complex, index, matrix, string, unit
	   *
	   * History:
	   *
	   *    v0.11    Created
	   *    v0.16    Added conversion from BigNumber
	   *    v14.2.1  Added conversion from bigint
	   *
	   * @param {string | number | boolean | Array | Matrix | null} value  A value of any type
	   * @return {boolean | Array | Matrix} The boolean value
	   */
	  return typed(name, {
	    '': function () {
	      return false;
	    },
	    boolean: function (x) {
	      return x;
	    },
	    number: function (x) {
	      return !!x;
	    },
	    null: function (x) {
	      return false;
	    },
	    BigNumber: function (x) {
	      return !x.isZero();
	    },
	    string: function (x) {
	      // try case insensitive
	      const lcase = x.toLowerCase();
	      if (lcase === 'true') {
	        return true;
	      } else if (lcase === 'false') {
	        return false;
	      }

	      // test whether value is a valid number
	      const num = Number(x);
	      if (x !== '' && !isNaN(num)) {
	        return !!num;
	      }
	      throw new Error('Cannot convert "' + x + '" to a boolean');
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return boolean$1;
}

var bignumber$1 = {};

var hasRequiredBignumber$1;

function requireBignumber$1 () {
	if (hasRequiredBignumber$1) return bignumber$1;
	hasRequiredBignumber$1 = 1;

	Object.defineProperty(bignumber$1, "__esModule", {
	  value: true
	});
	bignumber$1.createBignumber = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'bignumber';
	const dependencies = ['typed', 'BigNumber'];
	bignumber$1.createBignumber = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  /**
	   * Create a BigNumber, which can store numbers with arbitrary precision.
	   * When a matrix is provided, all elements will be converted to BigNumber.
	   *
	   * Syntax:
	   *
	   *    math.bignumber(x)
	   *
	   * Examples:
	   *
	   *    0.1 + 0.2                                  // returns number 0.30000000000000004
	   *    math.bignumber(0.1) + math.bignumber(0.2)  // returns BigNumber 0.3
	   *
	   *
	   *    7.2e500                                    // returns number Infinity
	   *    math.bignumber('7.2e500')                  // returns BigNumber 7.2e500
	   *
	   * See also:
	   *
	   *    number, bigint, boolean, complex, index, matrix, string, unit
	   *
	   * @param {number | string | Fraction | BigNumber | bigint | Array | Matrix | boolean | null} [value]  Value for the big number,
	   *                                                    0 by default.
	   * @returns {BigNumber} The created bignumber
	   */
	  return typed('bignumber', {
	    '': function () {
	      return new BigNumber(0);
	    },
	    number: function (x) {
	      // convert to string to prevent errors in case of >15 digits
	      return new BigNumber(x + '');
	    },
	    string: function (x) {
	      const wordSizeSuffixMatch = x.match(/(0[box][0-9a-fA-F]*)i([0-9]*)/);
	      if (wordSizeSuffixMatch) {
	        // x has a word size suffix
	        const size = wordSizeSuffixMatch[2];
	        const n = BigNumber(wordSizeSuffixMatch[1]);
	        const twoPowSize = new BigNumber(2).pow(Number(size));
	        if (n.gt(twoPowSize.sub(1))) {
	          throw new SyntaxError(`String "${x}" is out of range`);
	        }
	        const twoPowSizeSubOne = new BigNumber(2).pow(Number(size) - 1);
	        if (n.gte(twoPowSizeSubOne)) {
	          return n.sub(twoPowSize);
	        } else {
	          return n;
	        }
	      }
	      return new BigNumber(x);
	    },
	    BigNumber: function (x) {
	      // we assume a BigNumber is immutable
	      return x;
	    },
	    bigint: function (x) {
	      return new BigNumber(x.toString());
	    },
	    Unit: typed.referToSelf(self => x => {
	      const clone = x.clone();
	      clone.value = self(x.value);
	      return clone;
	    }),
	    Fraction: function (x) {
	      return new BigNumber(String(x.n)).div(String(x.d)).times(String(x.s));
	    },
	    null: function (_x) {
	      return new BigNumber(0);
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return bignumber$1;
}

var complex$1 = {};

var hasRequiredComplex$1;

function requireComplex$1 () {
	if (hasRequiredComplex$1) return complex$1;
	hasRequiredComplex$1 = 1;

	Object.defineProperty(complex$1, "__esModule", {
	  value: true
	});
	complex$1.createComplex = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'complex';
	const dependencies = ['typed', 'Complex'];
	complex$1.createComplex = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Complex
	  } = _ref;
	  /**
	   * Create a complex value or convert a value to a complex value.
	   *
	   * Syntax:
	   *
	   *     math.complex()                           // creates a complex value with zero
	   *                                              // as real and imaginary part.
	   *     math.complex(re : number, im : string)   // creates a complex value with provided
	   *                                              // values for real and imaginary part.
	   *     math.complex(re : number)                // creates a complex value with provided
	   *                                              // real value and zero imaginary part.
	   *     math.complex(complex : Complex)          // clones the provided complex value.
	   *     math.complex(arg : string)               // parses a string into a complex value.
	   *     math.complex(array : Array)              // converts the elements of the array
	   *                                              // or matrix element wise into a
	   *                                              // complex value.
	   *     math.complex({re: number, im: number})   // creates a complex value with provided
	   *                                              // values for real an imaginary part.
	   *     math.complex({r: number, phi: number})   // creates a complex value with provided
	   *                                              // polar coordinates
	   *
	   * Examples:
	   *
	   *    const a = math.complex(3, -4)     // a = Complex 3 - 4i
	   *    a.re = 5                          // a = Complex 5 - 4i
	   *    const i = a.im                    // Number -4
	   *    const b = math.complex('2 + 6i')  // Complex 2 + 6i
	   *    const c = math.complex()          // Complex 0 + 0i
	   *    const d = math.add(a, b)          // Complex 5 + 2i
	   *
	   * See also:
	   *
	   *    bignumber, boolean, index, matrix, number, string, unit
	   *
	   * History:
	   *
	   *    v0.5   Created
	   *    v0.16  Added conversion from BigNumber
	   *    v6     Added conversion from Fraction
	   *
	   * @param {* | Array | Matrix} [args]
	   *            Arguments specifying the real and imaginary part of the complex number
	   * @return {Complex | Array | Matrix} Returns a complex value
	   */
	  return typed('complex', {
	    '': function () {
	      return Complex.ZERO;
	    },
	    number: function (x) {
	      return new Complex(x, 0);
	    },
	    'number, number': function (re, im) {
	      return new Complex(re, im);
	    },
	    // TODO: this signature should be redundant
	    'BigNumber, BigNumber': function (re, im) {
	      return new Complex(re.toNumber(), im.toNumber());
	    },
	    Fraction: function (x) {
	      return new Complex(x.valueOf(), 0);
	    },
	    Complex: function (x) {
	      return x.clone();
	    },
	    string: function (x) {
	      return Complex(x); // for example '2 + 3i'
	    },
	    null: function (x) {
	      return Complex(0);
	    },
	    Object: function (x) {
	      if ('re' in x && 'im' in x) {
	        return new Complex(x.re, x.im);
	      }
	      if ('r' in x && 'phi' in x || 'abs' in x && 'arg' in x) {
	        return new Complex(x);
	      }
	      throw new Error('Expected object with properties (re and im) or (r and phi) or (abs and arg)');
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return complex$1;
}

var fraction$1 = {};

var hasRequiredFraction$1;

function requireFraction$1 () {
	if (hasRequiredFraction$1) return fraction$1;
	hasRequiredFraction$1 = 1;

	Object.defineProperty(fraction$1, "__esModule", {
	  value: true
	});
	fraction$1.createFraction = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'fraction';
	const dependencies = ['typed', 'Fraction'];
	fraction$1.createFraction = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Fraction
	  } = _ref;
	  /**
	   * Create a fraction or convert a value to a fraction.
	   *
	   * With one numeric argument, produces the closest rational approximation to the
	   * input.
	   * With two arguments, the first is the numerator and the second is the denominator,
	   * and creates the corresponding fraction. Both numerator and denominator must be
	   * integers.
	   * With one object argument, looks for the integer numerator as the value of property
	   * 'n' and the integer denominator as the value of property 'd'.
	   * With a matrix argument, creates a matrix of the same shape with entries
	   * converted into fractions.
	   *
	   * Syntax:
	   *     math.fraction(value)
	   *     math.fraction(numerator, denominator)
	   *     math.fraction({n: numerator, d: denominator})
	   *     math.fraction(matrix: Array | Matrix)
	   *
	   * Examples:
	   *
	   *     math.fraction(6.283)             // returns Fraction 6283/1000
	   *     math.fraction(1, 3)              // returns Fraction 1/3
	   *     math.fraction('2/3')             // returns Fraction 2/3
	   *     math.fraction({n: 2, d: 3})      // returns Fraction 2/3
	   *     math.fraction([0.2, 0.25, 1.25]) // returns Array [1/5, 1/4, 5/4]
	   *     math.fraction(4, 5.1)            // throws Error: Parameters must be integer
	   *
	   * See also:
	   *
	   *    bignumber, number, string, unit
	   *
	   * History:
	   *
	   *    v2     Created
	   *    v3     Added conversion from BigNumber
	   *    v11.8  Added conversion from Unit
	   *    v13    Added conversion from bigint
	   *    v14    Move to bigint-based fraction.js@5; construct from two bigints
	   *
	   * @param {number | string | Fraction | BigNumber | bigint | Unit | Array | Matrix} [args]
	   *            Arguments specifying the value, or numerator and denominator of
	   *            the fraction
	   * @return {Fraction | Array | Matrix} Returns a fraction
	   */
	  return typed('fraction', {
	    number: function (x) {
	      if (!Number.isFinite(x) || isNaN(x)) {
	        throw new Error(x + ' cannot be represented as a fraction');
	      }
	      return new Fraction(x);
	    },
	    string: function (x) {
	      return new Fraction(x);
	    },
	    'number, number': function (numerator, denominator) {
	      return new Fraction(numerator, denominator);
	    },
	    'bigint, bigint': function (numerator, denominator) {
	      return new Fraction(numerator, denominator);
	    },
	    null: function (x) {
	      return new Fraction(0);
	    },
	    BigNumber: function (x) {
	      return new Fraction(x.toString());
	    },
	    bigint: function (x) {
	      return new Fraction(x.toString());
	    },
	    Fraction: function (x) {
	      return x; // fractions are immutable
	    },
	    Unit: typed.referToSelf(self => x => {
	      const clone = x.clone();
	      clone.value = self(x.value);
	      return clone;
	    }),
	    Object: function (x) {
	      return new Fraction(x);
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return fraction$1;
}

var matrix$2 = {};

var hasRequiredMatrix$1;

function requireMatrix$1 () {
	if (hasRequiredMatrix$1) return matrix$2;
	hasRequiredMatrix$1 = 1;

	Object.defineProperty(matrix$2, "__esModule", {
	  value: true
	});
	matrix$2.createMatrix = void 0;
	var _factory = requireFactory$b();
	const name = 'matrix';
	const dependencies = ['typed', 'Matrix', 'DenseMatrix', 'SparseMatrix'];
	matrix$2.createMatrix = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Matrix,
	    DenseMatrix,
	    SparseMatrix
	  } = _ref;
	  /**
	   * Create a Matrix. The function creates a new `math.Matrix` object from
	   * an `Array`. A Matrix has utility functions to manipulate the data in the
	   * matrix, like getting the size and getting or setting values in the matrix.
	   * Supported storage formats are 'dense' and 'sparse'.
	   *
	   * Syntax:
	   *
	   *    math.matrix()                         // creates an empty matrix using default storage format (dense).
	   *    math.matrix(data)                     // creates a matrix with initial data using default storage format (dense).
	   *    math.matrix('dense')                  // creates an empty matrix using the given storage format.
	   *    math.matrix(data, 'dense')            // creates a matrix with initial data using the given storage format.
	   *    math.matrix(data, 'sparse')           // creates a sparse matrix with initial data.
	   *    math.matrix(data, 'sparse', 'number') // creates a sparse matrix with initial data, number data type.
	   *
	   * Examples:
	   *
	   *    let m = math.matrix([[1, 2], [3, 4]])
	   *    m.size()                        // Array [2, 2]
	   *    m.resize([3, 2], 5)
	   *    m.valueOf()                     // Array [[1, 2], [3, 4], [5, 5]]
	   *    m.get([1, 0])                    // number 3
	   *
	   * See also:
	   *
	   *    bignumber, boolean, complex, index, number, string, unit, sparse
	   *
	   * History:
	   *
	   *    v0.5  Created
	   *    v1.5  Support dense or sparse Matrix types; allow construction
	   *          from string, Array, or another Matrix.
	   *
	   * @param {Array | Matrix} [data]    A multi dimensional array
	   * @param {string} [format]          The Matrix storage format, either `'dense'` or `'sparse'`
	   * @param {string} [datatype]        Type of the values
	   *
	   * @return {Matrix} The created matrix
	   */
	  return typed(name, {
	    '': function () {
	      return _create([]);
	    },
	    string: function (format) {
	      return _create([], format);
	    },
	    'string, string': function (format, datatype) {
	      return _create([], format, datatype);
	    },
	    Array: function (data) {
	      return _create(data);
	    },
	    Matrix: function (data) {
	      return _create(data, data.storage());
	    },
	    'Array | Matrix, string': _create,
	    'Array | Matrix, string, string': _create
	  });

	  /**
	   * Create a new Matrix with given storage format
	   * @param {Array} data
	   * @param {string} [format]
	   * @param {string} [datatype]
	   * @returns {Matrix} Returns a new Matrix
	   * @private
	   */
	  function _create(data, format, datatype) {
	    // get storage format constructor
	    if (format === 'dense' || format === 'default' || format === undefined) {
	      return new DenseMatrix(data, datatype);
	    }
	    if (format === 'sparse') {
	      return new SparseMatrix(data, datatype);
	    }
	    throw new TypeError('Unknown matrix type ' + JSON.stringify(format) + '.');
	  }
	});
	return matrix$2;
}

var matrixFromFunction$1 = {};

var hasRequiredMatrixFromFunction$1;

function requireMatrixFromFunction$1 () {
	if (hasRequiredMatrixFromFunction$1) return matrixFromFunction$1;
	hasRequiredMatrixFromFunction$1 = 1;

	Object.defineProperty(matrixFromFunction$1, "__esModule", {
	  value: true
	});
	matrixFromFunction$1.createMatrixFromFunction = void 0;
	var _factory = requireFactory$b();
	const name = 'matrixFromFunction';
	const dependencies = ['typed', 'matrix', 'isZero'];
	matrixFromFunction$1.createMatrixFromFunction = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    isZero
	  } = _ref;
	  /**
	   * Create a matrix by evaluating a generating function at each index.
	   * The simplest overload returns a multi-dimensional array as long as `size`
	   * is an array.
	   * Passing `size` as a Matrix or specifying a `format` will result in
	   * returning a Matrix.
	   *
	   * Syntax:
	   *
	   *    math.matrixFromFunction(size, fn)
	   *    math.matrixFromFunction(size, fn, format)
	   *    math.matrixFromFunction(size, fn, format, datatype)
	   *    math.matrixFromFunction(size, format, fn)
	   *    math.matrixFromFunction(size, format, datatype, fn)
	   *
	   * Where:
	   *
	   *    - `size: (number[] | Matrix)`
	   *      A vector giving the extent of the array to be created in each
	   *      dimension. If size has one entry, a vector is created; if it
	   *      has two, a rectangular array/Matrix is created; if three, a
	   *      three-dimensional array/Matrix is created; and so on.
	   *    - `fn: (index: number[]) => MathType`
	   *      The callback function that will generate the entries of the
	   *      matrix. It is called in turn with the index of each entry of
	   *      the matrix. The index is always an ordinary array of numbers
	   *      with the same length as _size_. So for vectors, you will get
	   *      indices like `[0]` or `[1]`, whereas for matrices, you will
	   *      get indices like `[2, 0]` or `[1,3]`. The return value may
	   *      be any type that can go in an array or Matrix entry, although
	   *      if you supply the _datatype_ argument, you must yourself ensure
	   *      the type of the return value matches. Note that currently,
	   *      your callback _fn_ will receive 0-based indices for the matrix
	   *      entries, regardless of whether matrixFromFunction is invoked
	   *      directly from JavaScript or via the mathjs expression language.
	   *    - `format: 'dense'|'sparse'`
	   *      Specifies the storage format for the resulting Matrix. Note that
	   *      if this argument is given, the return value will always be a
	   *      Matrix (rather than possibly an Array).
	   *    - `datatype: string`
	   *      Specifies the data type of entries of the new matrix. If given,
	   *      it should be the name of a data type that mathjs supports, as
	   *      returned by the math.typeOf function. It is up to the caller
	   *      to make certain that all values returned by _fn_ are consistent
	   *      with this datatype if specified.
	   *
	   * Examples:
	   *
	   *    math.matrixFromFunction([3,3], i => i[0] - i[1]) // an antisymmetric matrix
	   *    math.matrixFromFunction([100, 100], 'sparse', i => i[0] - i[1] === 1 ? 4 : 0) // a sparse subdiagonal matrix
	   *    math.matrixFromFunction([5], i => math.random()) // a random vector
	   *
	   * See also:
	   *
	   *    matrix, typeOf, zeros
	   *
	   * @param {Array | Matrix} size   The size of the matrix to be created
	   * @param {function} fn           Callback function invoked for every entry in the matrix
	   * @param {string} [format]       The Matrix storage format, either `'dense'` or `'sparse'`
	   * @param {string} [datatype]     Type of the values
	   * @return {Array | Matrix} Returns the created matrix
	   */
	  return typed(name, {
	    'Array | Matrix, function, string, string': function (size, fn, format, datatype) {
	      return _create(size, fn, format, datatype);
	    },
	    'Array | Matrix, function, string': function (size, fn, format) {
	      return _create(size, fn, format);
	    },
	    'Matrix, function': function (size, fn) {
	      return _create(size, fn, 'dense');
	    },
	    'Array, function': function (size, fn) {
	      return _create(size, fn, 'dense').toArray();
	    },
	    'Array | Matrix, string, function': function (size, format, fn) {
	      return _create(size, fn, format);
	    },
	    'Array | Matrix, string, string, function': function (size, format, datatype, fn) {
	      return _create(size, fn, format, datatype);
	    }
	  });
	  function _create(size, fn, format, datatype) {
	    let m;
	    if (datatype !== undefined) {
	      m = matrix(format, datatype);
	    } else {
	      m = matrix(format);
	    }
	    m.resize(size);
	    m.forEach(function (_, index) {
	      const val = fn(index);
	      if (isZero(val)) return;
	      m.set(index, val);
	    });
	    return m;
	  }
	});
	return matrixFromFunction$1;
}

var matrixFromRows$1 = {};

var hasRequiredMatrixFromRows$1;

function requireMatrixFromRows$1 () {
	if (hasRequiredMatrixFromRows$1) return matrixFromRows$1;
	hasRequiredMatrixFromRows$1 = 1;

	Object.defineProperty(matrixFromRows$1, "__esModule", {
	  value: true
	});
	matrixFromRows$1.createMatrixFromRows = void 0;
	var _factory = requireFactory$b();
	const name = 'matrixFromRows';
	const dependencies = ['typed', 'matrix', 'flatten', 'size'];
	matrixFromRows$1.createMatrixFromRows = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    flatten,
	    size
	  } = _ref;
	  /**
	   * Create a dense matrix from vectors as individual rows.
	   * If you pass column vectors, they will be transposed (but not conjugated!)
	   *
	   * Syntax:
	   *
	   *    math.matrixFromRows(...arr)
	   *    math.matrixFromRows(row1, row2)
	   *    math.matrixFromRows(row1, row2, row3)
	   *
	   * Examples:
	   *
	   *    math.matrixFromRows([1, 2, 3], [[4],[5],[6]])
	   *    math.matrixFromRows(...vectors)
	   *
	   * See also:
	   *
	   *    matrix, matrixFromColumns, matrixFromFunction, zeros
	   *
	   * @param {... Array | Matrix} rows  Multiple rows
	   * @return { number[][] | Matrix } if at least one of the arguments is an array, an array will be returned
	   */
	  return typed(name, {
	    '...Array': function (arr) {
	      return _createArray(arr);
	    },
	    '...Matrix': function (arr) {
	      return matrix(_createArray(arr.map(m => m.toArray())));
	    }

	    // TODO implement this properly for SparseMatrix
	  });
	  function _createArray(arr) {
	    if (arr.length === 0) throw new TypeError('At least one row is needed to construct a matrix.');
	    const N = checkVectorTypeAndReturnLength(arr[0]);
	    const result = [];
	    for (const row of arr) {
	      const rowLength = checkVectorTypeAndReturnLength(row);
	      if (rowLength !== N) {
	        throw new TypeError('The vectors had different length: ' + (N | 0) + ' ≠ ' + (rowLength | 0));
	      }
	      result.push(flatten(row));
	    }
	    return result;
	  }
	  function checkVectorTypeAndReturnLength(vec) {
	    const s = size(vec);
	    if (s.length === 1) {
	      // 1D vector
	      return s[0];
	    } else if (s.length === 2) {
	      // 2D vector
	      if (s[0] === 1) {
	        // row vector
	        return s[1];
	      } else if (s[1] === 1) {
	        // col vector
	        return s[0];
	      } else {
	        throw new TypeError('At least one of the arguments is not a vector.');
	      }
	    } else {
	      throw new TypeError('Only one- or two-dimensional vectors are supported.');
	    }
	  }
	});
	return matrixFromRows$1;
}

var matrixFromColumns$1 = {};

var hasRequiredMatrixFromColumns$1;

function requireMatrixFromColumns$1 () {
	if (hasRequiredMatrixFromColumns$1) return matrixFromColumns$1;
	hasRequiredMatrixFromColumns$1 = 1;

	Object.defineProperty(matrixFromColumns$1, "__esModule", {
	  value: true
	});
	matrixFromColumns$1.createMatrixFromColumns = void 0;
	var _factory = requireFactory$b();
	const name = 'matrixFromColumns';
	const dependencies = ['typed', 'matrix', 'flatten', 'size'];
	matrixFromColumns$1.createMatrixFromColumns = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    flatten,
	    size
	  } = _ref;
	  /**
	   * Create a dense matrix from vectors as individual columns.
	   * If you pass row vectors, they will be transposed (but not conjugated!)
	   *
	   * Syntax:
	   *
	   *    math.matrixFromColumns(...arr)
	   *    math.matrixFromColumns(col1, col2)
	   *    math.matrixFromColumns(col1, col2, col3)
	   *
	   * Examples:
	   *
	   *    math.matrixFromColumns([1, 2, 3], [[4],[5],[6]])
	   *    math.matrixFromColumns(...vectors)
	   *
	   * See also:
	   *
	   *    matrix, matrixFromRows, matrixFromFunction, zeros
	   *
	   * @param {... Array | Matrix} cols Multiple columns
	   * @return { number[][] | Matrix } if at least one of the arguments is an array, an array will be returned
	   */
	  return typed(name, {
	    '...Array': function (arr) {
	      return _createArray(arr);
	    },
	    '...Matrix': function (arr) {
	      return matrix(_createArray(arr.map(m => m.toArray())));
	    }

	    // TODO implement this properly for SparseMatrix
	  });
	  function _createArray(arr) {
	    if (arr.length === 0) throw new TypeError('At least one column is needed to construct a matrix.');
	    const N = checkVectorTypeAndReturnLength(arr[0]);

	    // create an array with empty rows
	    const result = [];
	    for (let i = 0; i < N; i++) {
	      result[i] = [];
	    }

	    // loop columns
	    for (const col of arr) {
	      const colLength = checkVectorTypeAndReturnLength(col);
	      if (colLength !== N) {
	        throw new TypeError('The vectors had different length: ' + (N | 0) + ' ≠ ' + (colLength | 0));
	      }
	      const f = flatten(col);

	      // push a value to each row
	      for (let i = 0; i < N; i++) {
	        result[i].push(f[i]);
	      }
	    }
	    return result;
	  }
	  function checkVectorTypeAndReturnLength(vec) {
	    const s = size(vec);
	    if (s.length === 1) {
	      // 1D vector
	      return s[0];
	    } else if (s.length === 2) {
	      // 2D vector
	      if (s[0] === 1) {
	        // row vector
	        return s[1];
	      } else if (s[1] === 1) {
	        // col vector
	        return s[0];
	      } else {
	        throw new TypeError('At least one of the arguments is not a vector.');
	      }
	    } else {
	      throw new TypeError('Only one- or two-dimensional vectors are supported.');
	    }
	  }
	});
	return matrixFromColumns$1;
}

var splitUnit$1 = {};

var hasRequiredSplitUnit$1;

function requireSplitUnit$1 () {
	if (hasRequiredSplitUnit$1) return splitUnit$1;
	hasRequiredSplitUnit$1 = 1;

	Object.defineProperty(splitUnit$1, "__esModule", {
	  value: true
	});
	splitUnit$1.createSplitUnit = void 0;
	var _factory = requireFactory$b();
	const name = 'splitUnit';
	const dependencies = ['typed'];
	splitUnit$1.createSplitUnit = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Split a unit in an array of units whose sum is equal to the original unit.
	   *
	   * Syntax:
	   *
	   *     math.splitUnit(unit: Unit, parts: Array.<Unit>)
	   *
	   * Example:
	   *
	   *     math.splitUnit(new Unit(1, 'm'), ['feet', 'inch'])
	   *     // [ 3 feet, 3.3700787401575 inch ]
	   *
	   * See also:
	   *
	   *     unit
	   *
	   * @param {Array} [parts] An array of strings or valueless units.
	   * @return {Array} An array of units.
	   */
	  return typed(name, {
	    'Unit, Array': function (unit, parts) {
	      return unit.splitUnit(parts);
	    }
	  });
	});
	return splitUnit$1;
}

var unaryMinus$1 = {};

var hasRequiredUnaryMinus$1;

function requireUnaryMinus$1 () {
	if (hasRequiredUnaryMinus$1) return unaryMinus$1;
	hasRequiredUnaryMinus$1 = 1;

	Object.defineProperty(unaryMinus$1, "__esModule", {
	  value: true
	});
	unaryMinus$1.createUnaryMinus = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _index = requireNumber$2();
	const name = 'unaryMinus';
	const dependencies = ['typed'];
	unaryMinus$1.createUnaryMinus = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Inverse the sign of a value, apply a unary minus operation.
	   *
	   * For matrices, the function is evaluated element wise. Boolean values and
	   * strings will be converted to a number. For complex numbers, both real and
	   * complex value are inverted.
	   *
	   * Syntax:
	   *
	   *    math.unaryMinus(x)
	   *
	   * Examples:
	   *
	   *    math.unaryMinus(3.5)      // returns -3.5
	   *    math.unaryMinus(-4.2)     // returns 4.2
	   *
	   * See also:
	   *
	   *    add, subtract, unaryPlus
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} x Number to be inverted.
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} Returns the value with inverted sign.
	   */
	  return typed(name, {
	    number: _index.unaryMinusNumber,
	    'Complex | BigNumber | Fraction': x => x.neg(),
	    bigint: x => -x,
	    Unit: typed.referToSelf(self => x => {
	      const res = x.clone();
	      res.value = typed.find(self, res.valueType())(x.value);
	      return res;
	    }),
	    // deep map collection, skip zeros since unaryMinus(0) = 0
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self, true))

	    // TODO: add support for string
	  });
	});
	return unaryMinus$1;
}

var unaryPlus$1 = {};

var hasRequiredUnaryPlus$1;

function requireUnaryPlus$1 () {
	if (hasRequiredUnaryPlus$1) return unaryPlus$1;
	hasRequiredUnaryPlus$1 = 1;

	Object.defineProperty(unaryPlus$1, "__esModule", {
	  value: true
	});
	unaryPlus$1.createUnaryPlus = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _index = requireNumber$2();
	var _number = requireNumber$3();
	const name = 'unaryPlus';
	const dependencies = ['typed', 'config', 'numeric'];
	unaryPlus$1.createUnaryPlus = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    numeric
	  } = _ref;
	  /**
	   * Unary plus operation.
	   * Boolean values and strings will be converted to a number, numeric values will be returned as is.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.unaryPlus(x)
	   *
	   * Examples:
	   *
	   *    math.unaryPlus(3.5)      // returns 3.5
	   *    math.unaryPlus(1)     // returns 1
	   *
	   * See also:
	   *
	   *    unaryMinus, add, subtract
	   *
	   * @param  {number | BigNumber | bigint | Fraction | string | Complex | Unit | Array | Matrix} x
	   *            Input value
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix}
	   *            Returns the input value when numeric, converts to a number when input is non-numeric.
	   */
	  return typed(name, {
	    number: _index.unaryPlusNumber,
	    Complex: function (x) {
	      return x; // complex numbers are immutable
	    },
	    BigNumber: function (x) {
	      return x; // bignumbers are immutable
	    },
	    bigint: function (x) {
	      return x;
	    },
	    Fraction: function (x) {
	      return x; // fractions are immutable
	    },
	    Unit: function (x) {
	      return x.clone();
	    },
	    // deep map collection, skip zeros since unaryPlus(0) = 0
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self, true)),
	    boolean: function (x) {
	      return numeric(x ? 1 : 0, config.number);
	    },
	    string: function (x) {
	      return numeric(x, (0, _number.safeNumberType)(x, config));
	    }
	  });
	});
	return unaryPlus$1;
}

var abs$1 = {};

var hasRequiredAbs$1;

function requireAbs$1 () {
	if (hasRequiredAbs$1) return abs$1;
	hasRequiredAbs$1 = 1;

	Object.defineProperty(abs$1, "__esModule", {
	  value: true
	});
	abs$1.createAbs = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _index = requireNumber$2();
	const name = 'abs';
	const dependencies = ['typed'];
	abs$1.createAbs = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the absolute value of a number. For matrices, the function is
	   * evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.abs(x)
	   *
	   * Examples:
	   *
	   *    math.abs(3.5)                // returns number 3.5
	   *    math.abs(-4.2)               // returns number 4.2
	   *
	   *    math.abs([3, -5, -1, 0, 2])  // returns Array [3, 5, 1, 0, 2]
	   *
	   * See also:
	   *
	   *    sign
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Array | Matrix | Unit} x
	   *            A number or matrix for which to get the absolute value
	   * @return {number | BigNumber | bigint | Fraction | Complex | Array | Matrix | Unit}
	   *            Absolute value of `x`
	   */
	  return typed(name, {
	    number: _index.absNumber,
	    'Complex | BigNumber | Fraction | Unit': x => x.abs(),
	    bigint: x => x < 0n ? -x : x,
	    // deep map collection, skip zeros since abs(0) = 0
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self, true))
	  });
	});
	return abs$1;
}

var mapSlices$1 = {};

var hasRequiredMapSlices$1;

function requireMapSlices$1 () {
	if (hasRequiredMapSlices$1) return mapSlices$1;
	hasRequiredMapSlices$1 = 1;

	Object.defineProperty(mapSlices$1, "__esModule", {
	  value: true
	});
	mapSlices$1.createMapSlices = void 0;
	var _factory = requireFactory$b();
	var _array = requireArray();
	var _is = requireIs();
	var _IndexError = requireIndexError();
	const name = 'mapSlices';
	const dependencies = ['typed', 'isInteger'];
	mapSlices$1.createMapSlices = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isInteger
	  } = _ref;
	  /**
	   * Apply a function that maps an array to a scalar
	   * along a given axis of a matrix or array.
	   * Returns a new matrix or array with one less dimension than the input.
	   *
	   * Syntax:
	   *
	   *     math.mapSlices(A, dim, callback)
	   *
	   * Where:
	   *
	   * - `dim: number` is a zero-based dimension over which to concatenate the matrices.
	   *
	   * Examples:
	   *
	   *    const A = [[1, 2], [3, 4]]
	   *    const sum = math.sum
	   *
	   *    math.mapSlices(A, 0, sum)             // returns [4, 6]
	   *    math.mapSlices(A, 1, sum)             // returns [3, 7]
	   *
	   * See also:
	   *
	   *    map, filter, forEach
	   *
	   * Note:
	   *
	   *    `mapSlices()` is also currently available via its deprecated
	   *    synonym `apply()`.
	   *
	   * @param {Array | Matrix} array   The input Matrix
	   * @param {number} dim             The dimension along which the callback is applied
	   * @param {Function} callback      The callback function that is applied. This Function
	   *                                 should take an array or 1-d matrix as an input and
	   *                                 return a number.
	   * @return {Array | Matrix} res    The residual matrix with the function mapped on the slices over some dimension.
	   */
	  return typed(name, {
	    'Array | Matrix, number | BigNumber, function': function (mat, dim, callback) {
	      if (!isInteger(dim)) {
	        throw new TypeError('Integer number expected for dimension');
	      }
	      const size = Array.isArray(mat) ? (0, _array.arraySize)(mat) : mat.size();
	      if (dim < 0 || dim >= size.length) {
	        throw new _IndexError.IndexError(dim, size.length);
	      }
	      if ((0, _is.isMatrix)(mat)) {
	        return mat.create(_mapSlices(mat.valueOf(), dim, callback), mat.datatype());
	      } else {
	        return _mapSlices(mat, dim, callback);
	      }
	    }
	  });
	}, {
	  formerly: 'apply'
	});

	/**
	 * Recursively reduce a matrix
	 * @param {Array} mat
	 * @param {number} dim
	 * @param {Function} callback
	 * @returns {Array} ret
	 * @private
	 */
	function _mapSlices(mat, dim, callback) {
	  let i, ret, tran;
	  if (dim <= 0) {
	    if (!Array.isArray(mat[0])) {
	      return callback(mat);
	    } else {
	      tran = _switch(mat);
	      ret = [];
	      for (i = 0; i < tran.length; i++) {
	        ret[i] = _mapSlices(tran[i], dim - 1, callback);
	      }
	      return ret;
	    }
	  } else {
	    ret = [];
	    for (i = 0; i < mat.length; i++) {
	      ret[i] = _mapSlices(mat[i], dim - 1, callback);
	    }
	    return ret;
	  }
	}

	/**
	 * Transpose a matrix
	 * @param {Array} mat
	 * @returns {Array} ret
	 * @private
	 */
	function _switch(mat) {
	  const I = mat.length;
	  const J = mat[0].length;
	  let i, j;
	  const ret = [];
	  for (j = 0; j < J; j++) {
	    const tmp = [];
	    for (i = 0; i < I; i++) {
	      tmp.push(mat[i][j]);
	    }
	    ret.push(tmp);
	  }
	  return ret;
	}
	return mapSlices$1;
}

var addScalar = {};

var hasRequiredAddScalar;

function requireAddScalar () {
	if (hasRequiredAddScalar) return addScalar;
	hasRequiredAddScalar = 1;

	Object.defineProperty(addScalar, "__esModule", {
	  value: true
	});
	addScalar.createAddScalar = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'addScalar';
	const dependencies = ['typed'];
	addScalar.createAddScalar = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Add two scalar values, `x + y`.
	   * This function is meant for internal use: it is used by the public function
	   * `add`
	   *
	   * This function does not support collections (Array or Matrix).
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit} x   First value to add
	   * @param  {number | BigNumber | bigint | Fraction | Complex} y          Second value to add
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit}     Sum of `x` and `y`
	   * @private
	   */
	  return typed(name, {
	    'number, number': _index.addNumber,
	    'Complex, Complex': function (x, y) {
	      return x.add(y);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return x.plus(y);
	    },
	    'bigint, bigint': function (x, y) {
	      return x + y;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return x.add(y);
	    },
	    'Unit, Unit': typed.referToSelf(self => (x, y) => {
	      if (x.value === null || x.value === undefined) {
	        throw new Error('Parameter x contains a unit with undefined value');
	      }
	      if (y.value === null || y.value === undefined) {
	        throw new Error('Parameter y contains a unit with undefined value');
	      }
	      if (!x.equalBase(y)) throw new Error('Units do not match');
	      const res = x.clone();
	      res.value = typed.find(self, [res.valueType(), y.valueType()])(res.value, y.value);
	      res.fixPrefix = false;
	      return res;
	    })
	  });
	});
	return addScalar;
}

var subtractScalar = {};

var hasRequiredSubtractScalar;

function requireSubtractScalar () {
	if (hasRequiredSubtractScalar) return subtractScalar;
	hasRequiredSubtractScalar = 1;

	Object.defineProperty(subtractScalar, "__esModule", {
	  value: true
	});
	subtractScalar.createSubtractScalar = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'subtractScalar';
	const dependencies = ['typed'];
	subtractScalar.createSubtractScalar = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Subtract two scalar values, `x - y`.
	   * This function is meant for internal use: it is used by the public function
	   * `subtract`
	   *
	   * This function does not support collections (Array or Matrix).
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit} x   First value
	   * @param  {number | BigNumber | bigint | Fraction | Complex} y          Second value to be subtracted from `x`
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit}     Difference of `x` and `y`
	   * @private
	   */
	  return typed(name, {
	    'number, number': _index.subtractNumber,
	    'Complex, Complex': function (x, y) {
	      return x.sub(y);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return x.minus(y);
	    },
	    'bigint, bigint': function (x, y) {
	      return x - y;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return x.sub(y);
	    },
	    'Unit, Unit': typed.referToSelf(self => (x, y) => {
	      if (x.value === null || x.value === undefined) {
	        throw new Error('Parameter x contains a unit with undefined value');
	      }
	      if (y.value === null || y.value === undefined) {
	        throw new Error('Parameter y contains a unit with undefined value');
	      }
	      if (!x.equalBase(y)) throw new Error('Units do not match');
	      const res = x.clone();
	      res.value = typed.find(self, [res.valueType(), y.valueType()])(res.value, y.value);
	      res.fixPrefix = false;
	      return res;
	    })
	  });
	});
	return subtractScalar;
}

var cbrt$1 = {};

var hasRequiredCbrt$1;

function requireCbrt$1 () {
	if (hasRequiredCbrt$1) return cbrt$1;
	hasRequiredCbrt$1 = 1;

	Object.defineProperty(cbrt$1, "__esModule", {
	  value: true
	});
	cbrt$1.createCbrt = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _index = requireNumber$2();
	const name = 'cbrt';
	const dependencies = ['config', 'typed', 'isNegative', 'unaryMinus', 'matrix', 'Complex', 'BigNumber', 'Fraction'];
	cbrt$1.createCbrt = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    config,
	    typed,
	    isNegative,
	    unaryMinus,
	    matrix,
	    Complex,
	    BigNumber,
	    Fraction
	  } = _ref;
	  /**
	   * Calculate the cubic root of a value.
	   *
	   * To avoid confusion with the matrix cube root, this function does not
	   * apply to matrices. For a matrix, to take the cube root elementwise,
	   * see the examples.
	   *
	   * Syntax:
	   *
	   *    math.cbrt(x)
	   *    math.cbrt(x, allRoots)
	   *
	   * Examples:
	   *
	   *    math.cbrt(27)                  // returns 3
	   *    math.cube(3)                   // returns 27
	   *    math.cbrt(-64)                 // returns -4
	   *    math.cbrt(math.unit('27 m^3')) // returns Unit 3 m
	   *    math.map([27, 64, 125], x => math.cbrt(x))       // returns [3, 4, 5]
	   *
	   *    const x = math.complex('8i')
	   *    math.cbrt(x)                   // returns 1.7320508075689 + i ...
	   *    math.cbrt(x, true)
	   *    // Complex Matrix ["1.7320508075689+i", "-1.7320508075689+i", "-2i"]
	   *
	   * See also:
	   *
	   *    square, sqrt, cube
	   *
	   * History:
	   *
	   *    v2.3  Created
	   *
	   * @param {number | BigNumber | Complex | Unit} x
	   *            Value for which to calculate the cubic root.
	   * @param {boolean} [allRoots]  Optional, false by default. Only applicable
	   *            when `x` is a number or complex number. If true, all complex
	   *            roots are returned, if false (default) the principal root is
	   *            returned.
	   * @return {number | BigNumber | Complex | Unit}
	   *            Returns the cubic root of `x`
	   */
	  return typed(name, {
	    number: _index.cbrtNumber,
	    // note: signature 'number, boolean' is also supported,
	    //       created by typed as it knows how to convert number to Complex

	    Complex: _cbrtComplex,
	    'Complex, boolean': _cbrtComplex,
	    BigNumber: function (x) {
	      return x.cbrt();
	    },
	    Unit: _cbrtUnit
	  });

	  /**
	   * Calculate the cubic root for a complex number
	   * @param {Complex} x
	   * @param {boolean} [allRoots]   If true, the function will return an array
	   *                               with all three roots. If false or undefined,
	   *                               the principal root is returned.
	   * @returns {Complex | Array.<Complex> | Matrix.<Complex>} Returns the cubic root(s) of x
	   * @private
	   */
	  function _cbrtComplex(x, allRoots) {
	    // https://www.wikiwand.com/en/Cube_root#/Complex_numbers

	    const arg3 = x.arg() / 3;
	    const abs = x.abs();

	    // principal root:
	    const principal = new Complex((0, _index.cbrtNumber)(abs), 0).mul(new Complex(0, arg3).exp());
	    if (allRoots) {
	      const all = [principal, new Complex((0, _index.cbrtNumber)(abs), 0).mul(new Complex(0, arg3 + Math.PI * 2 / 3).exp()), new Complex((0, _index.cbrtNumber)(abs), 0).mul(new Complex(0, arg3 - Math.PI * 2 / 3).exp())];
	      return config.matrix === 'Array' ? all : matrix(all);
	    } else {
	      return principal;
	    }
	  }

	  /**
	   * Calculate the cubic root for a Unit
	   * @param {Unit} x
	   * @return {Unit} Returns the cubic root of x
	   * @private
	   */
	  function _cbrtUnit(x) {
	    if (x.value && (0, _is.isComplex)(x.value)) {
	      let result = x.clone();
	      result.value = 1.0;
	      result = result.pow(1.0 / 3); // Compute the units
	      result.value = _cbrtComplex(x.value); // Compute the value
	      return result;
	    } else {
	      const negate = isNegative(x.value);
	      if (negate) {
	        x.value = unaryMinus(x.value);
	      }

	      // TODO: create a helper function for this
	      let third;
	      if ((0, _is.isBigNumber)(x.value)) {
	        third = new BigNumber(1).div(3);
	      } else if ((0, _is.isFraction)(x.value)) {
	        third = new Fraction(1, 3);
	      } else {
	        third = 1 / 3;
	      }
	      const result = x.pow(third);
	      if (negate) {
	        result.value = unaryMinus(result.value);
	      }
	      return result;
	    }
	  }
	});
	return cbrt$1;
}

var ceil$1 = {};

var matAlgo11xS0s = {};

var hasRequiredMatAlgo11xS0s;

function requireMatAlgo11xS0s () {
	if (hasRequiredMatAlgo11xS0s) return matAlgo11xS0s;
	hasRequiredMatAlgo11xS0s = 1;

	Object.defineProperty(matAlgo11xS0s, "__esModule", {
	  value: true
	});
	matAlgo11xS0s.createMatAlgo11xS0s = void 0;
	var _factory = requireFactory$b();
	const name = 'matAlgo11xS0s';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo11xS0s.createMatAlgo11xS0s = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix S nonzero items and invokes the callback function f(Sij, b).
	   * Callback function invoked NZ times (number of nonzero items in S).
	   *
	   *
	   *          ┌  f(Sij, b)  ; S(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  0          ; otherwise
	   *
	   *
	   * @param {Matrix}   s                 The SparseMatrix instance (S)
	   * @param {Scalar}   b                 The Scalar value
	   * @param {Function} callback          The f(Aij,b) operation to invoke
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(b,Sij)
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * https://github.com/josdejong/mathjs/pull/346#issuecomment-97626813
	   */
	  return function matAlgo11xS0s(s, b, callback, inverse) {
	    // sparse matrix arrays
	    const avalues = s._values;
	    const aindex = s._index;
	    const aptr = s._ptr;
	    const asize = s._size;
	    const adt = s._datatype;

	    // sparse matrix cannot be a Pattern matrix
	    if (!avalues) {
	      throw new Error('Cannot perform operation on Pattern Sparse Matrix and Scalar value');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // convert b to the same datatype
	      b = typed.convert(b, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cvalues = [];
	    const cindex = [];
	    const cptr = [];

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // initialize ptr
	      cptr[j] = cindex.length;
	      // values in j
	      for (let k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        const i = aindex[k];
	        // invoke callback
	        const v = inverse ? cf(b, avalues[k]) : cf(avalues[k], b);
	        // check value is zero
	        if (!eq(v, zero)) {
	          // push index & value
	          cindex.push(i);
	          cvalues.push(v);
	        }
	      }
	    }
	    // update ptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return s.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: dt
	    });
	  };
	});
	return matAlgo11xS0s;
}

var matAlgo12xSfs = {};

var hasRequiredMatAlgo12xSfs;

function requireMatAlgo12xSfs () {
	if (hasRequiredMatAlgo12xSfs) return matAlgo12xSfs;
	hasRequiredMatAlgo12xSfs = 1;

	Object.defineProperty(matAlgo12xSfs, "__esModule", {
	  value: true
	});
	matAlgo12xSfs.createMatAlgo12xSfs = void 0;
	var _factory = requireFactory$b();
	const name = 'matAlgo12xSfs';
	const dependencies = ['typed', 'DenseMatrix'];
	matAlgo12xSfs.createMatAlgo12xSfs = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    DenseMatrix
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix S nonzero items and invokes the callback function f(Sij, b).
	   * Callback function invoked MxN times.
	   *
	   *
	   *          ┌  f(Sij, b)  ; S(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  f(0, b)    ; otherwise
	   *
	   *
	   * @param {Matrix}   s                 The SparseMatrix instance (S)
	   * @param {Scalar}   b                 The Scalar value
	   * @param {Function} callback          The f(Aij,b) operation to invoke
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(b,Sij)
	   *
	   * @return {Matrix}                    DenseMatrix (C)
	   *
	   * https://github.com/josdejong/mathjs/pull/346#issuecomment-97626813
	   */
	  return function matAlgo12xSfs(s, b, callback, inverse) {
	    // sparse matrix arrays
	    const avalues = s._values;
	    const aindex = s._index;
	    const aptr = s._ptr;
	    const asize = s._size;
	    const adt = s._datatype;

	    // sparse matrix cannot be a Pattern matrix
	    if (!avalues) {
	      throw new Error('Cannot perform operation on Pattern Sparse Matrix and Scalar value');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string') {
	      // datatype
	      dt = adt;
	      // convert b to the same datatype
	      b = typed.convert(b, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cdata = [];

	    // workspaces
	    const x = [];
	    // marks indicating we have a value in x for a given column
	    const w = [];

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // columns mark
	      const mark = j + 1;
	      // values in j
	      for (let k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        const r = aindex[k];
	        // update workspace
	        x[r] = avalues[k];
	        w[r] = mark;
	      }
	      // loop rows
	      for (let i = 0; i < rows; i++) {
	        // initialize C on first column
	        if (j === 0) {
	          // create row array
	          cdata[i] = [];
	        }
	        // check sparse matrix has a value @ i,j
	        if (w[i] === mark) {
	          // invoke callback, update C
	          cdata[i][j] = inverse ? cf(b, x[i]) : cf(x[i], b);
	        } else {
	          // dense matrix value @ i, j
	          cdata[i][j] = inverse ? cf(b, 0) : cf(0, b);
	        }
	      }
	    }

	    // return dense matrix
	    return new DenseMatrix({
	      data: cdata,
	      size: [rows, columns],
	      datatype: dt
	    });
	  };
	});
	return matAlgo12xSfs;
}

var matAlgo14xDs = {};

var hasRequiredMatAlgo14xDs;

function requireMatAlgo14xDs () {
	if (hasRequiredMatAlgo14xDs) return matAlgo14xDs;
	hasRequiredMatAlgo14xDs = 1;

	Object.defineProperty(matAlgo14xDs, "__esModule", {
	  value: true
	});
	matAlgo14xDs.createMatAlgo14xDs = void 0;
	var _factory = requireFactory$b();
	var _object = requireObject$3();
	const name = 'matAlgo14xDs';
	const dependencies = ['typed'];
	matAlgo14xDs.createMatAlgo14xDs = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Iterates over DenseMatrix items and invokes the callback function f(Aij..z, b).
	   * Callback function invoked MxN times.
	   *
	   * C(i,j,...z) = f(Aij..z, b)
	   *
	   * @param {Matrix}   a                 The DenseMatrix instance (A)
	   * @param {Scalar}   b                 The Scalar value
	   * @param {Function} callback          The f(Aij..z,b) operation to invoke
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(b,Aij..z)
	   *
	   * @return {Matrix}                    DenseMatrix (C)
	   *
	   * https://github.com/josdejong/mathjs/pull/346#issuecomment-97659042
	   */
	  return function matAlgo14xDs(a, b, callback, inverse) {
	    // a arrays
	    const adata = a._data;
	    const asize = a._size;
	    const adt = a._datatype;

	    // datatype
	    let dt;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string') {
	      // datatype
	      dt = adt;
	      // convert b to the same datatype
	      b = typed.convert(b, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // populate cdata, iterate through dimensions
	    const cdata = asize.length > 0 ? _iterate(cf, 0, asize, asize[0], adata, b, inverse) : [];

	    // c matrix
	    return a.createDenseMatrix({
	      data: cdata,
	      size: (0, _object.clone)(asize),
	      datatype: dt
	    });
	  };

	  // recursive function
	  function _iterate(f, level, s, n, av, bv, inverse) {
	    // initialize array for this level
	    const cv = [];
	    // check we reach the last level
	    if (level === s.length - 1) {
	      // loop arrays in last level
	      for (let i = 0; i < n; i++) {
	        // invoke callback and store value
	        cv[i] = inverse ? f(bv, av[i]) : f(av[i], bv);
	      }
	    } else {
	      // iterate current level
	      for (let j = 0; j < n; j++) {
	        // iterate next level
	        cv[j] = _iterate(f, level + 1, s, s[level + 1], av[j], bv, inverse);
	      }
	    }
	    return cv;
	  }
	});
	return matAlgo14xDs;
}

var hasRequiredCeil$1;

function requireCeil$1 () {
	if (hasRequiredCeil$1) return ceil$1;
	hasRequiredCeil$1 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(ceil$1, "__esModule", {
	  value: true
	});
	ceil$1.createCeilNumber = ceil$1.createCeil = void 0;
	var _decimal = _interopRequireDefault(requireDecimal());
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _number = requireNumber$3();
	var _nearlyEqual = requireNearlyEqual();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	const name = 'ceil';
	const dependencies = ['typed', 'config', 'round', 'matrix', 'equalScalar', 'zeros', 'DenseMatrix'];
	const bigTen = new _decimal.default(10);
	const createCeilNumber = ceil$1.createCeilNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config', 'round'], _ref => {
	  let {
	    typed,
	    config,
	    round
	  } = _ref;
	  function _ceilNumber(x) {
	    // See ./floor.js _floorNumber for rationale here
	    const c = Math.ceil(x);
	    const r = round(x);
	    if (c === r) return c;
	    if ((0, _number.nearlyEqual)(x, r, config.relTol, config.absTol) && !(0, _number.nearlyEqual)(x, c, config.relTol, config.absTol)) {
	      return r;
	    }
	    return c;
	  }
	  return typed(name, {
	    number: _ceilNumber,
	    'number, number': function (x, n) {
	      if (!(0, _number.isInteger)(n)) {
	        throw new RangeError('number of decimals in function ceil must be an integer');
	      }
	      if (n < 0 || n > 15) {
	        throw new RangeError('number of decimals in ceil number must be in range 0-15');
	      }
	      const shift = 10 ** n;
	      return _ceilNumber(x * shift) / shift;
	    }
	  });
	});
	ceil$1.createCeil = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref2 => {
	  let {
	    typed,
	    config,
	    round,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix
	  } = _ref2;
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const ceilNumber = createCeilNumber({
	    typed,
	    config,
	    round
	  });
	  function _bigCeil(x) {
	    // see ./floor.js _floorNumber for rationale
	    const bne = (a, b) => (0, _nearlyEqual.nearlyEqual)(a, b, config.relTol, config.absTol);
	    const c = x.ceil();
	    const r = round(x);
	    if (c.eq(r)) return c;
	    if (bne(x, r) && !bne(x, c)) return r;
	    return c;
	  }
	  /**
	   * Round a value towards plus infinity
	   * If `x` is complex, both real and imaginary part are rounded towards plus infinity.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.ceil(x)
	   *    math.ceil(x, n)
	   *    math.ceil(unit, valuelessUnit)
	   *    math.ceil(unit, n, valuelessUnit)
	   *
	   * Examples:
	   *
	   *    math.ceil(3.2)               // returns number 4
	   *    math.ceil(3.8)               // returns number 4
	   *    math.ceil(-4.2)              // returns number -4
	   *    math.ceil(-4.7)              // returns number -4
	   *
	   *    math.ceil(3.212, 2)          // returns number 3.22
	   *    math.ceil(3.288, 2)          // returns number 3.29
	   *    math.ceil(-4.212, 2)         // returns number -4.21
	   *    math.ceil(-4.782, 2)         // returns number -4.78
	   *
	   *    const c = math.complex(3.24, -2.71)
	   *    math.ceil(c)                 // returns Complex 4 - 2i ...
	   *    math.ceil(c, 1)              // returns Complex 3.3 - 2.7i
	   *
	   *    const unit = math.unit('3.241 cm')
	   *    const cm = math.unit('cm')
	   *    const mm = math.unit('mm')
	   *    math.ceil(unit, 1, cm)      // returns Unit 3.3 cm ...
	   *    math.ceil(unit, 1, mm)      // returns Unit 32.5 mm
	   *
	   *    math.ceil([3.2, 3.8, -4.7])  // returns Array [4, 4, -4]
	   *    math.ceil([3.21, 3.82, -4.71], 1)  // returns Array [3.3, 3.9, -4.7]
	   *
	   * See also:
	   *
	   *    floor, fix, round
	   *
	   * History:
	   *
	   *     v14    Handle Units
	   *     v7.4   Allow second "precision" argument
	   *     v5.7   Support tolerance for round-off errors
	   *     v2     Handle Fractions
	   *     v0.1   Created
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x  Value to be rounded
	   * @param  {number | BigNumber | Array} [n=0]                            Number of decimals
	   * @param  {Unit} [valuelessUnit]                                        A valueless unit
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} Rounded value
	   */
	  return typed('ceil', {
	    number: ceilNumber.signatures.number,
	    'number,number': ceilNumber.signatures['number,number'],
	    Complex: function (x) {
	      return x.ceil();
	    },
	    'Complex, number': function (x, n) {
	      return x.ceil(n);
	    },
	    'Complex, BigNumber': function (x, n) {
	      return x.ceil(n.toNumber());
	    },
	    BigNumber: _bigCeil,
	    'BigNumber, BigNumber': function (x, n) {
	      const shift = bigTen.pow(n);
	      return _bigCeil(x.mul(shift)).div(shift);
	    },
	    bigint: b => b,
	    'bigint, number': (b, _dummy) => b,
	    'bigint, BigNumber': (b, _dummy) => b,
	    Fraction: function (x) {
	      return x.ceil();
	    },
	    'Fraction, number': function (x, n) {
	      return x.ceil(n);
	    },
	    'Fraction, BigNumber': function (x, n) {
	      return x.ceil(n.toNumber());
	    },
	    'Unit, number, Unit': typed.referToSelf(self => function (x, n, unit) {
	      const valueless = x.toNumeric(unit);
	      return unit.multiply(self(valueless, n));
	    }),
	    'Unit, BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => self(x, n.toNumber(), unit)),
	    'Array | Matrix, number | BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => {
	      // deep map collection, skip zeros since ceil(0) = 0
	      return (0, _collection.deepMap)(x, value => self(value, n, unit), true);
	    }),
	    'Array | Matrix | Unit, Unit': typed.referToSelf(self => (x, unit) => self(x, 0, unit)),
	    'Array | Matrix': typed.referToSelf(self => x => {
	      // deep map collection, skip zeros since ceil(0) = 0
	      return (0, _collection.deepMap)(x, self, true);
	    }),
	    'Array, number | BigNumber': typed.referToSelf(self => (x, n) => {
	      // deep map collection, skip zeros since ceil(0) = 0
	      return (0, _collection.deepMap)(x, i => self(i, n), true);
	    }),
	    'SparseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      return matAlgo11xS0s(x, y, self, false);
	    }),
	    'DenseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      return matAlgo14xDs(x, y, self, false);
	    }),
	    'number | Complex | Fraction | BigNumber, Array': typed.referToSelf(self => (x, y) => {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(y), x, self, true).valueOf();
	    }),
	    'number | Complex | Fraction | BigNumber, Matrix': typed.referToSelf(self => (x, y) => {
	      if (equalScalar(x, 0)) return zeros(y.size(), y.storage());
	      if (y.storage() === 'dense') {
	        return matAlgo14xDs(y, x, self, true);
	      }
	      return matAlgo12xSfs(y, x, self, true);
	    })
	  });
	});
	return ceil$1;
}

var cube$1 = {};

var hasRequiredCube$1;

function requireCube$1 () {
	if (hasRequiredCube$1) return cube$1;
	hasRequiredCube$1 = 1;

	Object.defineProperty(cube$1, "__esModule", {
	  value: true
	});
	cube$1.createCube = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'cube';
	const dependencies = ['typed'];
	cube$1.createCube = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the cube of a value, `x * x * x`.
	   * To avoid confusion with `pow(M,3)`, this function does not apply to matrices.
	   * If you wish to cube every entry of a matrix, see the examples.
	   *
	   * Syntax:
	   *
	   *    math.cube(x)
	   *
	   * Examples:
	   *
	   *    math.cube(2)            // returns number 8
	   *    math.pow(2, 3)          // returns number 8
	   *    math.cube(4)            // returns number 64
	   *    4 * 4 * 4               // returns number 64
	   *
	   *    math.map([1, 2, 3, 4], math.cube) // returns Array [1, 8, 27, 64]
	   *
	   * See also:
	   *
	   *    multiply, square, pow, cbrt
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit} x  Number for which to calculate the cube
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit} Cube of x
	   */
	  return typed(name, {
	    number: _index.cubeNumber,
	    Complex: function (x) {
	      return x.mul(x).mul(x); // Is faster than pow(x, 3)
	    },
	    BigNumber: function (x) {
	      return x.times(x).times(x);
	    },
	    bigint: function (x) {
	      return x * x * x;
	    },
	    Fraction: function (x) {
	      return x.pow(3); // Is faster than mul()mul()mul()
	    },
	    Unit: function (x) {
	      return x.pow(3);
	    }
	  });
	});
	return cube$1;
}

var exp$1 = {};

var hasRequiredExp$1;

function requireExp$1 () {
	if (hasRequiredExp$1) return exp$1;
	hasRequiredExp$1 = 1;

	Object.defineProperty(exp$1, "__esModule", {
	  value: true
	});
	exp$1.createExp = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'exp';
	const dependencies = ['typed'];
	exp$1.createExp = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the exponential of a value.
	   * For matrices, if you want the matrix exponential of square matrix, use
	   * the `expm` function; if you want to take the exponential of each element,
	   * see the examples.
	   *
	   * Syntax:
	   *
	   *    math.exp(x)
	   *
	   * Examples:
	   *
	   *    math.exp(2)                  // returns number 7.3890560989306495
	   *    math.pow(math.e, 2)          // returns number 7.3890560989306495
	   *    math.log(math.exp(2))        // returns number 2
	   *
	   *    math.map([1, 2, 3], math.exp)
	   *    // returns [2.718281828459045, 7.3890560989306495, 20.085536923187668]
	   *
	   * See also:
	   *
	   *    expm1, expm, log, pow
	   *
	   * History:
	   *
	   *    v11     Don't apply elementwise, avoid confusion with matrix exponential
	   *    v0.20   Handle BigNumbers
	   *    v0.0.2  Created
	   *
	   * @param {number | BigNumber | Complex} x  A number to exponentiate
	   * @return {number | BigNumber | Complex} Exponential of `x`
	   */
	  return typed(name, {
	    number: _index.expNumber,
	    Complex: function (x) {
	      return x.exp();
	    },
	    BigNumber: function (x) {
	      return x.exp();
	    }
	  });
	});
	return exp$1;
}

var expm1$1 = {};

var hasRequiredExpm1$1;

function requireExpm1$1 () {
	if (hasRequiredExpm1$1) return expm1$1;
	hasRequiredExpm1$1 = 1;

	Object.defineProperty(expm1$1, "__esModule", {
	  value: true
	});
	expm1$1.createExpm1 = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'expm1';
	const dependencies = ['typed', 'Complex'];
	expm1$1.createExpm1 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the value of subtracting 1 from the exponential value.
	   * This function is more accurate than `math.exp(x)-1` when `x` is near 0
	   * To avoid ambiguity with the matrix exponential `expm`, this function
	   * does not operate on matrices; if you wish to apply it elementwise, see
	   * the examples.
	   *
	   * Syntax:
	   *
	   *    math.expm1(x)
	   *
	   * Examples:
	   *
	   *    math.expm1(2)                      // returns number 6.38905609893065
	   *    math.pow(math.e, 2) - 1            // returns number 6.3890560989306495
	   *    math.expm1(1e-8)                   // returns number 1.0000000050000001e-8
	   *    math.exp(1e-8) - 1                 // returns number 9.9999999392253e-9
	   *    math.log(math.expm1(2) + 1)        // returns number 2
	   *
	   *    math.map([1, 2, 3], math.expm1)
	   *    // returns [1.718281828459045, 6.3890560989306495, 19.085536923187668]
	   *
	   * See also:
	   *
	   *    exp, expm, log, pow
	   *
	   * History:
	   *
	   *    v11    Don't apply elementwise, avoids confusion with matrix exponential
	   *    v4.2   Created
	   *
	   * @param {number | BigNumber | Complex} x  The number to exponentiate
	   * @return {number | BigNumber | Complex} Exponential of `x`, minus one
	   */
	  return typed(name, {
	    number: _index.expm1Number,
	    Complex: function (x) {
	      const r = Math.exp(x.re);
	      return new Complex(r * Math.cos(x.im) - 1, r * Math.sin(x.im));
	    },
	    BigNumber: function (x) {
	      return x.exp().minus(1);
	    }
	  });
	});
	return expm1$1;
}

var fix$1 = {};

var hasRequiredFix$1;

function requireFix$1 () {
	if (hasRequiredFix$1) return fix$1;
	hasRequiredFix$1 = 1;

	Object.defineProperty(fix$1, "__esModule", {
	  value: true
	});
	fix$1.createFixNumber = fix$1.createFix = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	const name = 'fix';
	const dependencies = ['typed', 'Complex', 'matrix', 'ceil', 'floor', 'equalScalar', 'zeros', 'DenseMatrix'];
	const createFixNumber = fix$1.createFixNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'ceil', 'floor'], _ref => {
	  let {
	    typed,
	    ceil,
	    floor
	  } = _ref;
	  return typed(name, {
	    number: function (x) {
	      return x > 0 ? floor(x) : ceil(x);
	    },
	    'number, number': function (x, n) {
	      return x > 0 ? floor(x, n) : ceil(x, n);
	    }
	  });
	});
	fix$1.createFix = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref2 => {
	  let {
	    typed,
	    Complex,
	    matrix,
	    ceil,
	    floor,
	    equalScalar,
	    zeros,
	    DenseMatrix
	  } = _ref2;
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const fixNumber = createFixNumber({
	    typed,
	    ceil,
	    floor
	  });
	  /**
	   * Round a value towards zero.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.fix(x)
	   *    math.fix(x,n)
	   *    math.fix(unit, valuelessUnit)
	   *    math.fix(unit, n, valuelessUnit)
	   *
	   * Examples:
	   *
	   *    math.fix(3.2)                // returns number 3
	   *    math.fix(3.8)                // returns number 3
	   *    math.fix(-4.2)               // returns number -4
	   *    math.fix(-4.7)               // returns number -4
	   *
	   *    math.fix(3.12, 1)                // returns number 3.1
	   *    math.fix(3.18, 1)                // returns number 3.1
	   *    math.fix(-4.12, 1)               // returns number -4.1
	   *    math.fix(-4.17, 1)               // returns number -4.1
	   *
	   *    const c = math.complex(3.22, -2.78)
	   *    math.fix(c)                  // returns Complex 3 - 2i ...
	   *    math.fix(c, 1)               // returns Complex 3.2 -2.7i
	   *
	   *    const unit = math.unit('3.241 cm')
	   *    const cm = math.unit('cm')
	   *    const mm = math.unit('mm')
	   *    math.fix(unit, 1, cm)      // returns Unit 3.2 cm ...
	   *    math.fix(unit, 1, mm)      // returns Unit 32.4 mm
	   *
	   *    math.fix([3.2, 3.8, -4.7])      // returns Array [3, 3, -4]
	   *    math.fix([3.2, 3.8, -4.7], 1)   // returns Array [3.2, 3.8, -4.7]
	   *
	   * See also:
	   *
	   *    ceil, floor, round
	   *
	   * History:
	   *
	   *     v14    Handle Units
	   *     v7.4   Allow second "precision" argument
	   *     v5.7   Support tolerance for round-off errors
	   *     v2     Handle Fractions
	   *     v0.1   Created
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x  Value to be rounded
	   * @param  {number | BigNumber | Array} [n=0]                            Number of decimals
	   * @param  {Unit} [valuelessUnit]                                        A valueless unit
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} Rounded value
	   */
	  return typed('fix', {
	    number: fixNumber.signatures.number,
	    'number, number | BigNumber': fixNumber.signatures['number,number'],
	    Complex: function (x) {
	      return new Complex(x.re > 0 ? Math.floor(x.re) : Math.ceil(x.re), x.im > 0 ? Math.floor(x.im) : Math.ceil(x.im));
	    },
	    'Complex, number': function (x, n) {
	      return new Complex(x.re > 0 ? floor(x.re, n) : ceil(x.re, n), x.im > 0 ? floor(x.im, n) : ceil(x.im, n));
	    },
	    'Complex, BigNumber': function (x, bn) {
	      const n = bn.toNumber();
	      return new Complex(x.re > 0 ? floor(x.re, n) : ceil(x.re, n), x.im > 0 ? floor(x.im, n) : ceil(x.im, n));
	    },
	    BigNumber: function (x) {
	      return x.isNegative() ? ceil(x) : floor(x);
	    },
	    'BigNumber, number | BigNumber': function (x, n) {
	      return x.isNegative() ? ceil(x, n) : floor(x, n);
	    },
	    bigint: b => b,
	    'bigint, number': (b, _dummy) => b,
	    'bigint, BigNumber': (b, _dummy) => b,
	    Fraction: function (x) {
	      return x.s < 0n ? x.ceil() : x.floor();
	    },
	    'Fraction, number | BigNumber': function (x, n) {
	      return x.s < 0n ? ceil(x, n) : floor(x, n);
	    },
	    'Unit, number, Unit': typed.referToSelf(self => function (x, n, unit) {
	      const valueless = x.toNumeric(unit);
	      return unit.multiply(self(valueless, n));
	    }),
	    'Unit, BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => self(x, n.toNumber(), unit)),
	    'Array | Matrix, number | BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => {
	      // deep map collection, skip zeros since fix(0) = 0
	      return (0, _collection.deepMap)(x, value => self(value, n, unit), true);
	    }),
	    'Array | Matrix | Unit, Unit': typed.referToSelf(self => (x, unit) => self(x, 0, unit)),
	    'Array | Matrix': typed.referToSelf(self => x => {
	      // deep map collection, skip zeros since fix(0) = 0
	      return (0, _collection.deepMap)(x, self, true);
	    }),
	    'Array | Matrix, number | BigNumber': typed.referToSelf(self => (x, n) => {
	      // deep map collection, skip zeros since fix(0) = 0
	      return (0, _collection.deepMap)(x, i => self(i, n), true);
	    }),
	    'number | Complex | Fraction | BigNumber, Array': typed.referToSelf(self => (x, y) => {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(y), x, self, true).valueOf();
	    }),
	    'number | Complex | Fraction | BigNumber, Matrix': typed.referToSelf(self => (x, y) => {
	      if (equalScalar(x, 0)) return zeros(y.size(), y.storage());
	      if (y.storage() === 'dense') {
	        return matAlgo14xDs(y, x, self, true);
	      }
	      return matAlgo12xSfs(y, x, self, true);
	    })
	  });
	});
	return fix$1;
}

var floor$1 = {};

var hasRequiredFloor$1;

function requireFloor$1 () {
	if (hasRequiredFloor$1) return floor$1;
	hasRequiredFloor$1 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(floor$1, "__esModule", {
	  value: true
	});
	floor$1.createFloorNumber = floor$1.createFloor = void 0;
	var _decimal = _interopRequireDefault(requireDecimal());
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _number = requireNumber$3();
	var _nearlyEqual = requireNearlyEqual();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	const name = 'floor';
	const dependencies = ['typed', 'config', 'round', 'matrix', 'equalScalar', 'zeros', 'DenseMatrix'];
	const bigTen = new _decimal.default(10);
	const createFloorNumber = floor$1.createFloorNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config', 'round'], _ref => {
	  let {
	    typed,
	    config,
	    round
	  } = _ref;
	  function _floorNumber(x) {
	    // First, if the floor and the round are identical we can be
	    // quite comfortable that is the best answer:
	    const f = Math.floor(x);
	    const r = round(x);
	    if (f === r) return f;
	    // OK, they are different. If x is truly distinct from f but
	    // appears indistinguishable from r, presume it really is just
	    // the integer r with rounding/computation error, and return that
	    if ((0, _number.nearlyEqual)(x, r, config.relTol, config.absTol) && !(0, _number.nearlyEqual)(x, f, config.relTol, config.absTol)) {
	      return r;
	    }
	    // Otherwise (x distinct from both r and f, or indistinguishable from
	    // both r and f) may as well just return f, as that's the best
	    // candidate we can discern:
	    return f;
	  }
	  return typed(name, {
	    number: _floorNumber,
	    'number, number': function (x, n) {
	      if (!(0, _number.isInteger)(n)) {
	        throw new RangeError('number of decimals in function floor must be an integer');
	      }
	      if (n < 0 || n > 15) {
	        throw new RangeError('number of decimals in floor number must be in range 0 - 15');
	      }
	      const shift = 10 ** n;
	      return _floorNumber(x * shift) / shift;
	    }
	  });
	});
	floor$1.createFloor = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref2 => {
	  let {
	    typed,
	    config,
	    round,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix
	  } = _ref2;
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const floorNumber = createFloorNumber({
	    typed,
	    config,
	    round
	  });
	  function _bigFloor(x) {
	    // see _floorNumber above for rationale
	    const bne = (a, b) => (0, _nearlyEqual.nearlyEqual)(a, b, config.relTol, config.absTol);
	    const f = x.floor();
	    const r = round(x);
	    if (f.eq(r)) return f;
	    if (bne(x, r) && !bne(x, f)) return r;
	    return f;
	  }
	  /**
	   * Round a value towards minus infinity.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.floor(x)
	   *    math.floor(x, n)
	   *    math.floor(unit, valuelessUnit)
	   *    math.floor(unit, n, valuelessUnit)
	   *
	   * Examples:
	   *
	   *    math.floor(3.2)              // returns number 3
	   *    math.floor(3.8)              // returns number 3
	   *    math.floor(-4.2)             // returns number -5
	   *    math.floor(-4.7)             // returns number -5
	   *
	   *    math.floor(3.212, 2)          // returns number 3.21
	   *    math.floor(3.288, 2)          // returns number 3.28
	   *    math.floor(-4.212, 2)         // returns number -4.22
	   *    math.floor(-4.782, 2)         // returns number -4.79
	   *
	   *    const c = math.complex(3.24, -2.71)
	   *    math.floor(c)                 // returns Complex 3 - 3i ...
	   *    math.floor(c, 1)              // returns Complex 3.2 -2.8i
	   *
	   *    const unit = math.unit('3.241 cm')
	   *    const cm = math.unit('cm')
	   *    const mm = math.unit('mm')
	   *    math.floor(unit, 1, cm)      // returns Unit 3.2 cm ...
	   *    math.floor(unit, 1, mm)      // returns Unit 32.4 mm
	   *
	   *    math.floor([3.2, 3.8, -4.7])       // returns Array [3, 3, -5]
	   *    math.floor([3.21, 3.82, -4.71], 1)  // returns Array [3.2, 3.8, -4.8]
	   *
	   *    math.floor(math.tau, [2, 3])  // returns Array [6.28, 6.283]
	   *
	   *    // Note that floor(array, array) currently not implemented.
	   *
	   * See also:
	   *
	   *    ceil, fix, round
	   *
	   * History:
	   *
	   *     v14    Handle Units
	   *     v7.4   Allow second "precision" argument
	   *     v5.7   Support tolerance for round-off errors
	   *     v2     Handle Fractions
	   *     v0.1   Created
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x  Value to be rounded
	   * @param  {number | BigNumber | Array} [n=0]                            Number of decimals
	   * @param  {Unit} [valuelessUnit]                                        A valueless unit
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} Rounded value
	   */
	  return typed('floor', {
	    number: floorNumber.signatures.number,
	    'number,number': floorNumber.signatures['number,number'],
	    Complex: function (x) {
	      return x.floor();
	    },
	    'Complex, number': function (x, n) {
	      return x.floor(n);
	    },
	    'Complex, BigNumber': function (x, n) {
	      return x.floor(n.toNumber());
	    },
	    BigNumber: _bigFloor,
	    'BigNumber, BigNumber': function (x, n) {
	      const shift = bigTen.pow(n);
	      return _bigFloor(x.mul(shift)).div(shift);
	    },
	    bigint: b => b,
	    'bigint, number': (b, _dummy) => b,
	    'bigint, BigNumber': (b, _dummy) => b,
	    Fraction: function (x) {
	      return x.floor();
	    },
	    'Fraction, number': function (x, n) {
	      return x.floor(n);
	    },
	    'Fraction, BigNumber': function (x, n) {
	      return x.floor(n.toNumber());
	    },
	    'Unit, number, Unit': typed.referToSelf(self => function (x, n, unit) {
	      const valueless = x.toNumeric(unit);
	      return unit.multiply(self(valueless, n));
	    }),
	    'Unit, BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => self(x, n.toNumber(), unit)),
	    'Array | Matrix, number | BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => {
	      // deep map collection, skip zeros since floor(0) = 0
	      return (0, _collection.deepMap)(x, value => self(value, n, unit), true);
	    }),
	    'Array | Matrix | Unit, Unit': typed.referToSelf(self => (x, unit) => self(x, 0, unit)),
	    'Array | Matrix': typed.referToSelf(self => x => {
	      // deep map collection, skip zeros since floor(0) = 0
	      return (0, _collection.deepMap)(x, self, true);
	    }),
	    'Array, number | BigNumber': typed.referToSelf(self => (x, n) => {
	      // deep map collection, skip zeros since ceil(0) = 0
	      return (0, _collection.deepMap)(x, i => self(i, n), true);
	    }),
	    'SparseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      return matAlgo11xS0s(x, y, self, false);
	    }),
	    'DenseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      return matAlgo14xDs(x, y, self, false);
	    }),
	    'number | Complex | Fraction | BigNumber, Array': typed.referToSelf(self => (x, y) => {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(y), x, self, true).valueOf();
	    }),
	    'number | Complex | Fraction | BigNumber, Matrix': typed.referToSelf(self => (x, y) => {
	      if (equalScalar(x, 0)) return zeros(y.size(), y.storage());
	      if (y.storage() === 'dense') {
	        return matAlgo14xDs(y, x, self, true);
	      }
	      return matAlgo12xSfs(y, x, self, true);
	    })
	  });
	});
	return floor$1;
}

var gcd$1 = {};

var mod$2 = {};

var matAlgo02xDS0 = {};

var hasRequiredMatAlgo02xDS0;

function requireMatAlgo02xDS0 () {
	if (hasRequiredMatAlgo02xDS0) return matAlgo02xDS0;
	hasRequiredMatAlgo02xDS0 = 1;

	Object.defineProperty(matAlgo02xDS0, "__esModule", {
	  value: true
	});
	matAlgo02xDS0.createMatAlgo02xDS0 = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo02xDS0';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo02xDS0.createMatAlgo02xDS0 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix nonzero items and invokes the callback function f(Dij, Sij).
	   * Callback function invoked NNZ times (number of nonzero items in SparseMatrix).
	   *
	   *
	   *          ┌  f(Dij, Sij)  ; S(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  0            ; otherwise
	   *
	   *
	   * @param {Matrix}   denseMatrix       The DenseMatrix instance (D)
	   * @param {Matrix}   sparseMatrix      The SparseMatrix instance (S)
	   * @param {Function} callback          The f(Dij,Sij) operation to invoke, where Dij = DenseMatrix(i,j) and Sij = SparseMatrix(i,j)
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(Sij,Dij)
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97477571
	   */
	  return function matAlgo02xDS0(denseMatrix, sparseMatrix, callback, inverse) {
	    // dense matrix arrays
	    const adata = denseMatrix._data;
	    const asize = denseMatrix._size;
	    const adt = denseMatrix._datatype || denseMatrix.getDataType();
	    // sparse matrix arrays
	    const bvalues = sparseMatrix._values;
	    const bindex = sparseMatrix._index;
	    const bptr = sparseMatrix._ptr;
	    const bsize = sparseMatrix._size;
	    const bdt = sparseMatrix._datatype || sparseMatrix._data === undefined ? sparseMatrix._datatype : sparseMatrix.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // sparse matrix cannot be a Pattern matrix
	    if (!bvalues) {
	      throw new Error('Cannot perform operation on Dense Matrix and Pattern Sparse Matrix');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result (SparseMatrix)
	    const cvalues = [];
	    const cindex = [];
	    const cptr = [];

	    // loop columns in b
	    for (let j = 0; j < columns; j++) {
	      // update cptr
	      cptr[j] = cindex.length;
	      // values in column j
	      for (let k0 = bptr[j], k1 = bptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        const i = bindex[k];
	        // update C(i,j)
	        const cij = inverse ? cf(bvalues[k], adata[i][j]) : cf(adata[i][j], bvalues[k]);
	        // check for nonzero
	        if (!eq(cij, zero)) {
	          // push i & v
	          cindex.push(i);
	          cvalues.push(cij);
	        }
	      }
	    }
	    // update cptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return sparseMatrix.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === denseMatrix._datatype && bdt === sparseMatrix._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo02xDS0;
}

var matAlgo03xDSf = {};

var hasRequiredMatAlgo03xDSf;

function requireMatAlgo03xDSf () {
	if (hasRequiredMatAlgo03xDSf) return matAlgo03xDSf;
	hasRequiredMatAlgo03xDSf = 1;

	Object.defineProperty(matAlgo03xDSf, "__esModule", {
	  value: true
	});
	matAlgo03xDSf.createMatAlgo03xDSf = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo03xDSf';
	const dependencies = ['typed'];
	matAlgo03xDSf.createMatAlgo03xDSf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix items and invokes the callback function f(Dij, Sij).
	   * Callback function invoked M*N times.
	   *
	   *
	   *          ┌  f(Dij, Sij)  ; S(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  f(Dij, 0)    ; otherwise
	   *
	   *
	   * @param {Matrix}   denseMatrix       The DenseMatrix instance (D)
	   * @param {Matrix}   sparseMatrix      The SparseMatrix instance (C)
	   * @param {Function} callback          The f(Dij,Sij) operation to invoke, where Dij = DenseMatrix(i,j) and Sij = SparseMatrix(i,j)
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(Sij,Dij)
	   *
	   * @return {Matrix}                    DenseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97477571
	   */
	  return function matAlgo03xDSf(denseMatrix, sparseMatrix, callback, inverse) {
	    // dense matrix arrays
	    const adata = denseMatrix._data;
	    const asize = denseMatrix._size;
	    const adt = denseMatrix._datatype || denseMatrix.getDataType();
	    // sparse matrix arrays
	    const bvalues = sparseMatrix._values;
	    const bindex = sparseMatrix._index;
	    const bptr = sparseMatrix._ptr;
	    const bsize = sparseMatrix._size;
	    const bdt = sparseMatrix._datatype || sparseMatrix._data === undefined ? sparseMatrix._datatype : sparseMatrix.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // sparse matrix cannot be a Pattern matrix
	    if (!bvalues) {
	      throw new Error('Cannot perform operation on Dense Matrix and Pattern Sparse Matrix');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result (DenseMatrix)
	    const cdata = [];

	    // initialize dense matrix
	    for (let z = 0; z < rows; z++) {
	      // initialize row
	      cdata[z] = [];
	    }

	    // workspace
	    const x = [];
	    // marks indicating we have a value in x for a given column
	    const w = [];

	    // loop columns in b
	    for (let j = 0; j < columns; j++) {
	      // column mark
	      const mark = j + 1;
	      // values in column j
	      for (let k0 = bptr[j], k1 = bptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        const i = bindex[k];
	        // update workspace
	        x[i] = inverse ? cf(bvalues[k], adata[i][j]) : cf(adata[i][j], bvalues[k]);
	        w[i] = mark;
	      }
	      // process workspace
	      for (let y = 0; y < rows; y++) {
	        // check we have a calculated value for current row
	        if (w[y] === mark) {
	          // use calculated value
	          cdata[y][j] = x[y];
	        } else {
	          // calculate value
	          cdata[y][j] = inverse ? cf(zero, adata[y][j]) : cf(adata[y][j], zero);
	        }
	      }
	    }

	    // return dense matrix
	    return denseMatrix.createDenseMatrix({
	      data: cdata,
	      size: [rows, columns],
	      datatype: adt === denseMatrix._datatype && bdt === sparseMatrix._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo03xDSf;
}

var matAlgo05xSfSf = {};

var hasRequiredMatAlgo05xSfSf;

function requireMatAlgo05xSfSf () {
	if (hasRequiredMatAlgo05xSfSf) return matAlgo05xSfSf;
	hasRequiredMatAlgo05xSfSf = 1;

	Object.defineProperty(matAlgo05xSfSf, "__esModule", {
	  value: true
	});
	matAlgo05xSfSf.createMatAlgo05xSfSf = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo05xSfSf';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo05xSfSf.createMatAlgo05xSfSf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix A and SparseMatrix B nonzero items and invokes the callback function f(Aij, Bij).
	   * Callback function invoked MAX(NNZA, NNZB) times
	   *
	   *
	   *          ┌  f(Aij, Bij)  ; A(i,j) !== 0 || B(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  0            ; otherwise
	   *
	   *
	   * @param {Matrix}   a                 The SparseMatrix instance (A)
	   * @param {Matrix}   b                 The SparseMatrix instance (B)
	   * @param {Function} callback          The f(Aij,Bij) operation to invoke
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97620294
	   */
	  return function matAlgo05xSfSf(a, b, callback) {
	    // sparse matrix arrays
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const asize = a._size;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // sparse matrix arrays
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cvalues = avalues && bvalues ? [] : undefined;
	    const cindex = [];
	    const cptr = [];

	    // workspaces
	    const xa = cvalues ? [] : undefined;
	    const xb = cvalues ? [] : undefined;
	    // marks indicating we have a value in x for a given column
	    const wa = [];
	    const wb = [];

	    // vars
	    let i, j, k, k1;

	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // update cptr
	      cptr[j] = cindex.length;
	      // columns mark
	      const mark = j + 1;
	      // loop values A(:,j)
	      for (k = aptr[j], k1 = aptr[j + 1]; k < k1; k++) {
	        // row
	        i = aindex[k];
	        // push index
	        cindex.push(i);
	        // update workspace
	        wa[i] = mark;
	        // check we need to process values
	        if (xa) {
	          xa[i] = avalues[k];
	        }
	      }
	      // loop values B(:,j)
	      for (k = bptr[j], k1 = bptr[j + 1]; k < k1; k++) {
	        // row
	        i = bindex[k];
	        // check row existed in A
	        if (wa[i] !== mark) {
	          // push index
	          cindex.push(i);
	        }
	        // update workspace
	        wb[i] = mark;
	        // check we need to process values
	        if (xb) {
	          xb[i] = bvalues[k];
	        }
	      }
	      // check we need to process values (non pattern matrix)
	      if (cvalues) {
	        // initialize first index in j
	        k = cptr[j];
	        // loop index in j
	        while (k < cindex.length) {
	          // row
	          i = cindex[k];
	          // marks
	          const wai = wa[i];
	          const wbi = wb[i];
	          // check Aij or Bij are nonzero
	          if (wai === mark || wbi === mark) {
	            // matrix values @ i,j
	            const va = wai === mark ? xa[i] : zero;
	            const vb = wbi === mark ? xb[i] : zero;
	            // Cij
	            const vc = cf(va, vb);
	            // check for zero
	            if (!eq(vc, zero)) {
	              // push value
	              cvalues.push(vc);
	              // increment pointer
	              k++;
	            } else {
	              // remove value @ i, do not increment pointer
	              cindex.splice(k, 1);
	            }
	          }
	        }
	      }
	    }
	    // update cptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo05xSfSf;
}

var matrixAlgorithmSuite = {};

var matAlgo13xDD = {};

var hasRequiredMatAlgo13xDD;

function requireMatAlgo13xDD () {
	if (hasRequiredMatAlgo13xDD) return matAlgo13xDD;
	hasRequiredMatAlgo13xDD = 1;

	Object.defineProperty(matAlgo13xDD, "__esModule", {
	  value: true
	});
	matAlgo13xDD.createMatAlgo13xDD = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo13xDD';
	const dependencies = ['typed'];
	matAlgo13xDD.createMatAlgo13xDD = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Iterates over DenseMatrix items and invokes the callback function f(Aij..z, Bij..z).
	   * Callback function invoked MxN times.
	   *
	   * C(i,j,...z) = f(Aij..z, Bij..z)
	   *
	   * @param {Matrix}   a                 The DenseMatrix instance (A)
	   * @param {Matrix}   b                 The DenseMatrix instance (B)
	   * @param {Function} callback          The f(Aij..z,Bij..z) operation to invoke
	   *
	   * @return {Matrix}                    DenseMatrix (C)
	   *
	   * https://github.com/josdejong/mathjs/pull/346#issuecomment-97658658
	   */
	  return function matAlgo13xDD(a, b, callback) {
	    // a arrays
	    const adata = a._data;
	    const asize = a._size;
	    const adt = a._datatype;
	    // b arrays
	    const bdata = b._data;
	    const bsize = b._size;
	    const bdt = b._datatype;
	    // c arrays
	    const csize = [];

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // validate each one of the dimension sizes
	    for (let s = 0; s < asize.length; s++) {
	      // must match
	      if (asize[s] !== bsize[s]) {
	        throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	      }
	      // update dimension in c
	      csize[s] = asize[s];
	    }

	    // datatype
	    let dt;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt) {
	      // datatype
	      dt = adt;
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // populate cdata, iterate through dimensions
	    const cdata = csize.length > 0 ? _iterate(cf, 0, csize, csize[0], adata, bdata) : [];

	    // c matrix
	    return a.createDenseMatrix({
	      data: cdata,
	      size: csize,
	      datatype: dt
	    });
	  };

	  // recursive function
	  function _iterate(f, level, s, n, av, bv) {
	    // initialize array for this level
	    const cv = [];
	    // check we reach the last level
	    if (level === s.length - 1) {
	      // loop arrays in last level
	      for (let i = 0; i < n; i++) {
	        // invoke callback and store value
	        cv[i] = f(av[i], bv[i]);
	      }
	    } else {
	      // iterate current level
	      for (let j = 0; j < n; j++) {
	        // iterate next level
	        cv[j] = _iterate(f, level + 1, s, s[level + 1], av[j], bv[j]);
	      }
	    }
	    return cv;
	  }
	});
	return matAlgo13xDD;
}

var broadcast = {};

var hasRequiredBroadcast;

function requireBroadcast () {
	if (hasRequiredBroadcast) return broadcast;
	hasRequiredBroadcast = 1;

	Object.defineProperty(broadcast, "__esModule", {
	  value: true
	});
	broadcast.broadcast = broadcast$1;
	var _array = requireArray();
	var _object = requireObject$3();
	/**
	* Broadcasts two matrices, and return both in an array
	* It checks if it's possible with broadcasting rules
	*
	* @param {Matrix}   A      First Matrix
	* @param {Matrix}   B      Second Matrix
	*
	* @return {Matrix[]}      [ broadcastedA, broadcastedB ]
	*/

	function broadcast$1(A, B) {
	  if ((0, _object.deepStrictEqual)(A.size(), B.size())) {
	    // If matrices have the same size return them
	    return [A, B];
	  }

	  // calculate the broadcasted sizes
	  const newSize = (0, _array.broadcastSizes)(A.size(), B.size());

	  // return the array with the two broadcasted matrices
	  return [A, B].map(M => _broadcastTo(M, newSize));
	}

	/**
	 * Broadcasts a matrix to the given size.
	 *
	 * @param {Matrix} M - The matrix to be broadcasted.
	 * @param {number[]} size - The desired size of the broadcasted matrix.
	 * @returns {Matrix} The broadcasted matrix.
	 * @throws {Error} If the size parameter is not an array of numbers.
	 */
	function _broadcastTo(M, size) {
	  if ((0, _object.deepStrictEqual)(M.size(), size)) {
	    return M;
	  }
	  return M.create((0, _array.broadcastTo)(M.valueOf(), size), M.datatype());
	}
	return broadcast;
}

var hasRequiredMatrixAlgorithmSuite;

function requireMatrixAlgorithmSuite () {
	if (hasRequiredMatrixAlgorithmSuite) return matrixAlgorithmSuite;
	hasRequiredMatrixAlgorithmSuite = 1;

	Object.defineProperty(matrixAlgorithmSuite, "__esModule", {
	  value: true
	});
	matrixAlgorithmSuite.createMatrixAlgorithmSuite = void 0;
	var _factory = requireFactory$b();
	var _object = requireObject$3();
	var _matAlgo13xDD = requireMatAlgo13xDD();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _broadcast = requireBroadcast();
	const name = 'matrixAlgorithmSuite';
	const dependencies = ['typed', 'matrix'];
	matrixAlgorithmSuite.createMatrixAlgorithmSuite = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix
	  } = _ref;
	  const matAlgo13xDD = (0, _matAlgo13xDD.createMatAlgo13xDD)({
	    typed
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });

	  /**
	   * Return a signatures object with the usual boilerplate of
	   * matrix algorithms, based on a plain options object with the
	   * following properties:
	   *   elop: function -- the elementwise operation to use, defaults to self
	   *   SS: function -- the algorithm to apply for two sparse matrices
	   *   DS: function -- the algorithm to apply for a dense and a sparse matrix
	   *   SD: function -- algo for a sparse and a dense; defaults to SD flipped
	   *   Ss: function -- the algorithm to apply for a sparse matrix and scalar
	   *   sS: function -- algo for scalar and sparse; defaults to Ss flipped
	   *   scalar: string -- typed-function type for scalars, defaults to 'any'
	   *
	   * If Ss is not specified, no matrix-scalar signatures are generated.
	   *
	   * @param {object} options
	   * @return {Object<string, function>} signatures
	   */
	  return function matrixAlgorithmSuite(options) {
	    const elop = options.elop;
	    const SD = options.SD || options.DS;
	    let matrixSignatures;
	    if (elop) {
	      // First the dense ones
	      matrixSignatures = {
	        'DenseMatrix, DenseMatrix': (x, y) => matAlgo13xDD(...(0, _broadcast.broadcast)(x, y), elop),
	        'Array, Array': (x, y) => matAlgo13xDD(...(0, _broadcast.broadcast)(matrix(x), matrix(y)), elop).valueOf(),
	        'Array, DenseMatrix': (x, y) => matAlgo13xDD(...(0, _broadcast.broadcast)(matrix(x), y), elop),
	        'DenseMatrix, Array': (x, y) => matAlgo13xDD(...(0, _broadcast.broadcast)(x, matrix(y)), elop)
	      };
	      // Now incorporate sparse matrices
	      if (options.SS) {
	        matrixSignatures['SparseMatrix, SparseMatrix'] = (x, y) => options.SS(...(0, _broadcast.broadcast)(x, y), elop, false);
	      }
	      if (options.DS) {
	        matrixSignatures['DenseMatrix, SparseMatrix'] = (x, y) => options.DS(...(0, _broadcast.broadcast)(x, y), elop, false);
	        matrixSignatures['Array, SparseMatrix'] = (x, y) => options.DS(...(0, _broadcast.broadcast)(matrix(x), y), elop, false);
	      }
	      if (SD) {
	        matrixSignatures['SparseMatrix, DenseMatrix'] = (x, y) => SD(...(0, _broadcast.broadcast)(y, x), elop, true);
	        matrixSignatures['SparseMatrix, Array'] = (x, y) => SD(...(0, _broadcast.broadcast)(matrix(y), x), elop, true);
	      }
	    } else {
	      // No elop, use this
	      // First the dense ones
	      matrixSignatures = {
	        'DenseMatrix, DenseMatrix': typed.referToSelf(self => (x, y) => {
	          return matAlgo13xDD(...(0, _broadcast.broadcast)(x, y), self);
	        }),
	        'Array, Array': typed.referToSelf(self => (x, y) => {
	          return matAlgo13xDD(...(0, _broadcast.broadcast)(matrix(x), matrix(y)), self).valueOf();
	        }),
	        'Array, DenseMatrix': typed.referToSelf(self => (x, y) => {
	          return matAlgo13xDD(...(0, _broadcast.broadcast)(matrix(x), y), self);
	        }),
	        'DenseMatrix, Array': typed.referToSelf(self => (x, y) => {
	          return matAlgo13xDD(...(0, _broadcast.broadcast)(x, matrix(y)), self);
	        })
	      };
	      // Now incorporate sparse matrices
	      if (options.SS) {
	        matrixSignatures['SparseMatrix, SparseMatrix'] = typed.referToSelf(self => (x, y) => {
	          return options.SS(...(0, _broadcast.broadcast)(x, y), self, false);
	        });
	      }
	      if (options.DS) {
	        matrixSignatures['DenseMatrix, SparseMatrix'] = typed.referToSelf(self => (x, y) => {
	          return options.DS(...(0, _broadcast.broadcast)(x, y), self, false);
	        });
	        matrixSignatures['Array, SparseMatrix'] = typed.referToSelf(self => (x, y) => {
	          return options.DS(...(0, _broadcast.broadcast)(matrix(x), y), self, false);
	        });
	      }
	      if (SD) {
	        matrixSignatures['SparseMatrix, DenseMatrix'] = typed.referToSelf(self => (x, y) => {
	          return SD(...(0, _broadcast.broadcast)(y, x), self, true);
	        });
	        matrixSignatures['SparseMatrix, Array'] = typed.referToSelf(self => (x, y) => {
	          return SD(...(0, _broadcast.broadcast)(matrix(y), x), self, true);
	        });
	      }
	    }

	    // Now add the scalars
	    const scalar = options.scalar || 'any';
	    const Ds = options.Ds || options.Ss;
	    if (Ds) {
	      if (elop) {
	        matrixSignatures['DenseMatrix,' + scalar] = (x, y) => matAlgo14xDs(x, y, elop, false);
	        matrixSignatures[scalar + ', DenseMatrix'] = (x, y) => matAlgo14xDs(y, x, elop, true);
	        matrixSignatures['Array,' + scalar] = (x, y) => matAlgo14xDs(matrix(x), y, elop, false).valueOf();
	        matrixSignatures[scalar + ', Array'] = (x, y) => matAlgo14xDs(matrix(y), x, elop, true).valueOf();
	      } else {
	        matrixSignatures['DenseMatrix,' + scalar] = typed.referToSelf(self => (x, y) => {
	          return matAlgo14xDs(x, y, self, false);
	        });
	        matrixSignatures[scalar + ', DenseMatrix'] = typed.referToSelf(self => (x, y) => {
	          return matAlgo14xDs(y, x, self, true);
	        });
	        matrixSignatures['Array,' + scalar] = typed.referToSelf(self => (x, y) => {
	          return matAlgo14xDs(matrix(x), y, self, false).valueOf();
	        });
	        matrixSignatures[scalar + ', Array'] = typed.referToSelf(self => (x, y) => {
	          return matAlgo14xDs(matrix(y), x, self, true).valueOf();
	        });
	      }
	    }
	    const sS = options.sS !== undefined ? options.sS : options.Ss;
	    if (elop) {
	      if (options.Ss) {
	        matrixSignatures['SparseMatrix,' + scalar] = (x, y) => options.Ss(x, y, elop, false);
	      }
	      if (sS) {
	        matrixSignatures[scalar + ', SparseMatrix'] = (x, y) => sS(y, x, elop, true);
	      }
	    } else {
	      if (options.Ss) {
	        matrixSignatures['SparseMatrix,' + scalar] = typed.referToSelf(self => (x, y) => {
	          return options.Ss(x, y, self, false);
	        });
	      }
	      if (sS) {
	        matrixSignatures[scalar + ', SparseMatrix'] = typed.referToSelf(self => (x, y) => {
	          return sS(y, x, self, true);
	        });
	      }
	    }
	    // Also pull in the scalar signatures if the operator is a typed function
	    if (elop && elop.signatures) {
	      (0, _object.extend)(matrixSignatures, elop.signatures);
	    }
	    return matrixSignatures;
	  };
	});
	return matrixAlgorithmSuite;
}

var hasRequiredMod$1;

function requireMod$1 () {
	if (hasRequiredMod$1) return mod$2;
	hasRequiredMod$1 = 1;

	Object.defineProperty(mod$2, "__esModule", {
	  value: true
	});
	mod$2.createMod = void 0;
	var _factory = requireFactory$b();
	var _floor = requireFloor$1();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo05xSfSf = requireMatAlgo05xSfSf();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'mod';
	const dependencies = ['typed', 'config', 'round', 'matrix', 'equalScalar', 'zeros', 'DenseMatrix', 'concat'];
	mod$2.createMod = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    round,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const floor = (0, _floor.createFloor)({
	    typed,
	    config,
	    round,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix
	  });
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo05xSfSf = (0, _matAlgo05xSfSf.createMatAlgo05xSfSf)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Calculates the modulus, the remainder of an integer division.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * The modulus is defined as:
	   *
	   *     x - y * floor(x / y)
	   *
	   * See https://en.wikipedia.org/wiki/Modulo_operation.
	   *
	   * Syntax:
	   *
	   *    math.mod(x, y)
	   *
	   * Examples:
	   *
	   *    math.mod(8, 3)                // returns 2
	   *    math.mod(11, 2)               // returns 1
	   *
	   *    function isOdd(x) {
	   *      return math.mod(x, 2) != 0
	   *    }
	   *    isOdd(2)                      // returns false...
	   *    isOdd(3)                      // returns true
	   *
	   * See also:
	   *
	   *    divide
	   *
	   * History:
	   *
	   *    v13    Handle bigints
	   *    v11.6  Support matrix broadcasting
	   *    v2     Handle Fractions
	   *    v0.2   Created
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Array | Matrix} x Dividend
	   * @param  {number | BigNumber | bigint | Fraction | Array | Matrix} y Divisor
	   * @return {number | BigNumber | bigint | Fraction | Array | Matrix} Returns the remainder of `x` divided by `y`.
	   */
	  return typed(name, {
	    'number, number': _modNumber,
	    'BigNumber, BigNumber': function (x, y) {
	      return y.isZero() ? x : x.sub(y.mul(floor(x.div(y))));
	    },
	    'bigint, bigint': function (x, y) {
	      if (y === 0n) {
	        return x;
	      }
	      if (x < 0) {
	        const m = x % y;
	        return m === 0n ? m : m + y;
	      }
	      return x % y;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return y.equals(0) ? x : x.sub(y.mul(floor(x.div(y))));
	    }
	  }, matrixAlgorithmSuite({
	    SS: matAlgo05xSfSf,
	    DS: matAlgo03xDSf,
	    SD: matAlgo02xDS0,
	    Ss: matAlgo11xS0s,
	    sS: matAlgo12xSfs
	  }));

	  /**
	  * Calculate the modulus of two numbers
	  * @param {number} x
	  * @param {number} y
	  * @returns {number} res
	  * @private
	  */
	  function _modNumber(x, y) {
	    // We don't use JavaScript's % operator here as this doesn't work
	    // correctly for x < 0 and x === 0
	    // see https://en.wikipedia.org/wiki/Modulo_operation

	    // We use mathjs floor to handle errors associated with
	    // precision float approximation
	    return y === 0 ? x : x - y * floor(x / y);
	  }
	});
	return mod$2;
}

var matAlgo01xDSid = {};

var hasRequiredMatAlgo01xDSid;

function requireMatAlgo01xDSid () {
	if (hasRequiredMatAlgo01xDSid) return matAlgo01xDSid;
	hasRequiredMatAlgo01xDSid = 1;

	Object.defineProperty(matAlgo01xDSid, "__esModule", {
	  value: true
	});
	matAlgo01xDSid.createMatAlgo01xDSid = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo01xDSid';
	const dependencies = ['typed'];
	matAlgo01xDSid.createMatAlgo01xDSid = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix nonzero items and invokes the callback function f(Dij, Sij).
	   * Callback function invoked NNZ times (number of nonzero items in SparseMatrix).
	   *
	   *
	   *          ┌  f(Dij, Sij)  ; S(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  Dij          ; otherwise
	   *
	   *
	   * @param {Matrix}   denseMatrix       The DenseMatrix instance (D)
	   * @param {Matrix}   sparseMatrix      The SparseMatrix instance (S)
	   * @param {Function} callback          The f(Dij,Sij) operation to invoke, where Dij = DenseMatrix(i,j) and Sij = SparseMatrix(i,j)
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(Sij,Dij)
	   *
	   * @return {Matrix}                    DenseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97477571
	   */
	  return function algorithm1(denseMatrix, sparseMatrix, callback, inverse) {
	    // dense matrix arrays
	    const adata = denseMatrix._data;
	    const asize = denseMatrix._size;
	    const adt = denseMatrix._datatype || denseMatrix.getDataType();
	    // sparse matrix arrays
	    const bvalues = sparseMatrix._values;
	    const bindex = sparseMatrix._index;
	    const bptr = sparseMatrix._ptr;
	    const bsize = sparseMatrix._size;
	    const bdt = sparseMatrix._datatype || sparseMatrix._data === undefined ? sparseMatrix._datatype : sparseMatrix.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // sparse matrix cannot be a Pattern matrix
	    if (!bvalues) {
	      throw new Error('Cannot perform operation on Dense Matrix and Pattern Sparse Matrix');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // process data types
	    const dt = typeof adt === 'string' && adt !== 'mixed' && adt === bdt ? adt : undefined;
	    // callback function
	    const cf = dt ? typed.find(callback, [dt, dt]) : callback;

	    // vars
	    let i, j;

	    // result (DenseMatrix)
	    const cdata = [];
	    // initialize c
	    for (i = 0; i < rows; i++) {
	      cdata[i] = [];
	    }

	    // workspace
	    const x = [];
	    // marks indicating we have a value in x for a given column
	    const w = [];

	    // loop columns in b
	    for (j = 0; j < columns; j++) {
	      // column mark
	      const mark = j + 1;
	      // values in column j
	      for (let k0 = bptr[j], k1 = bptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        i = bindex[k];
	        // update workspace
	        x[i] = inverse ? cf(bvalues[k], adata[i][j]) : cf(adata[i][j], bvalues[k]);
	        // mark i as updated
	        w[i] = mark;
	      }
	      // loop rows
	      for (i = 0; i < rows; i++) {
	        // check row is in workspace
	        if (w[i] === mark) {
	          // c[i][j] was already calculated
	          cdata[i][j] = x[i];
	        } else {
	          // item does not exist in S
	          cdata[i][j] = adata[i][j];
	        }
	      }
	    }

	    // return dense matrix
	    return denseMatrix.createDenseMatrix({
	      data: cdata,
	      size: [rows, columns],
	      datatype: adt === denseMatrix._datatype && bdt === sparseMatrix._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo01xDSid;
}

var matAlgo04xSidSid = {};

var hasRequiredMatAlgo04xSidSid;

function requireMatAlgo04xSidSid () {
	if (hasRequiredMatAlgo04xSidSid) return matAlgo04xSidSid;
	hasRequiredMatAlgo04xSidSid = 1;

	Object.defineProperty(matAlgo04xSidSid, "__esModule", {
	  value: true
	});
	matAlgo04xSidSid.createMatAlgo04xSidSid = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo04xSidSid';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo04xSidSid.createMatAlgo04xSidSid = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix A and SparseMatrix B nonzero items and invokes the callback function f(Aij, Bij).
	   * Callback function invoked MAX(NNZA, NNZB) times
	   *
	   *
	   *          ┌  f(Aij, Bij)  ; A(i,j) !== 0 && B(i,j) !== 0
	   * C(i,j) = ┤  A(i,j)       ; A(i,j) !== 0 && B(i,j) === 0
	   *          └  B(i,j)       ; A(i,j) === 0
	   *
	   *
	   * @param {Matrix}   a                 The SparseMatrix instance (A)
	   * @param {Matrix}   b                 The SparseMatrix instance (B)
	   * @param {Function} callback          The f(Aij,Bij) operation to invoke
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97620294
	   */
	  return function matAlgo04xSidSid(a, b, callback) {
	    // sparse matrix arrays
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const asize = a._size;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // sparse matrix arrays
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cvalues = avalues && bvalues ? [] : undefined;
	    const cindex = [];
	    const cptr = [];

	    // workspace
	    const xa = avalues && bvalues ? [] : undefined;
	    const xb = avalues && bvalues ? [] : undefined;
	    // marks indicating we have a value in x for a given column
	    const wa = [];
	    const wb = [];

	    // vars
	    let i, j, k, k0, k1;

	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // update cptr
	      cptr[j] = cindex.length;
	      // columns mark
	      const mark = j + 1;
	      // loop A(:,j)
	      for (k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        i = aindex[k];
	        // update c
	        cindex.push(i);
	        // update workspace
	        wa[i] = mark;
	        // check we need to process values
	        if (xa) {
	          xa[i] = avalues[k];
	        }
	      }
	      // loop B(:,j)
	      for (k0 = bptr[j], k1 = bptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        i = bindex[k];
	        // check row exists in A
	        if (wa[i] === mark) {
	          // update record in xa @ i
	          if (xa) {
	            // invoke callback
	            const v = cf(xa[i], bvalues[k]);
	            // check for zero
	            if (!eq(v, zero)) {
	              // update workspace
	              xa[i] = v;
	            } else {
	              // remove mark (index will be removed later)
	              wa[i] = null;
	            }
	          }
	        } else {
	          // update c
	          cindex.push(i);
	          // update workspace
	          wb[i] = mark;
	          // check we need to process values
	          if (xb) {
	            xb[i] = bvalues[k];
	          }
	        }
	      }
	      // check we need to process values (non pattern matrix)
	      if (xa && xb) {
	        // initialize first index in j
	        k = cptr[j];
	        // loop index in j
	        while (k < cindex.length) {
	          // row
	          i = cindex[k];
	          // check workspace has value @ i
	          if (wa[i] === mark) {
	            // push value (Aij != 0 || (Aij != 0 && Bij != 0))
	            cvalues[k] = xa[i];
	            // increment pointer
	            k++;
	          } else if (wb[i] === mark) {
	            // push value (bij != 0)
	            cvalues[k] = xb[i];
	            // increment pointer
	            k++;
	          } else {
	            // remove index @ k
	            cindex.splice(k, 1);
	          }
	        }
	      }
	    }
	    // update cptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo04xSidSid;
}

var matAlgo10xSids = {};

var hasRequiredMatAlgo10xSids;

function requireMatAlgo10xSids () {
	if (hasRequiredMatAlgo10xSids) return matAlgo10xSids;
	hasRequiredMatAlgo10xSids = 1;

	Object.defineProperty(matAlgo10xSids, "__esModule", {
	  value: true
	});
	matAlgo10xSids.createMatAlgo10xSids = void 0;
	var _factory = requireFactory$b();
	const name = 'matAlgo10xSids';
	const dependencies = ['typed', 'DenseMatrix'];
	matAlgo10xSids.createMatAlgo10xSids = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    DenseMatrix
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix S nonzero items and invokes the callback function f(Sij, b).
	   * Callback function invoked NZ times (number of nonzero items in S).
	   *
	   *
	   *          ┌  f(Sij, b)  ; S(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  b          ; otherwise
	   *
	   *
	   * @param {Matrix}   s                 The SparseMatrix instance (S)
	   * @param {Scalar}   b                 The Scalar value
	   * @param {Function} callback          The f(Aij,b) operation to invoke
	   * @param {boolean}  inverse           A true value indicates callback should be invoked f(b,Sij)
	   *
	   * @return {Matrix}                    DenseMatrix (C)
	   *
	   * https://github.com/josdejong/mathjs/pull/346#issuecomment-97626813
	   */
	  return function matAlgo10xSids(s, b, callback, inverse) {
	    // sparse matrix arrays
	    const avalues = s._values;
	    const aindex = s._index;
	    const aptr = s._ptr;
	    const asize = s._size;
	    const adt = s._datatype;

	    // sparse matrix cannot be a Pattern matrix
	    if (!avalues) {
	      throw new Error('Cannot perform operation on Pattern Sparse Matrix and Scalar value');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string') {
	      // datatype
	      dt = adt;
	      // convert b to the same datatype
	      b = typed.convert(b, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cdata = [];

	    // workspaces
	    const x = [];
	    // marks indicating we have a value in x for a given column
	    const w = [];

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // columns mark
	      const mark = j + 1;
	      // values in j
	      for (let k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        const r = aindex[k];
	        // update workspace
	        x[r] = avalues[k];
	        w[r] = mark;
	      }
	      // loop rows
	      for (let i = 0; i < rows; i++) {
	        // initialize C on first column
	        if (j === 0) {
	          // create row array
	          cdata[i] = [];
	        }
	        // check sparse matrix has a value @ i,j
	        if (w[i] === mark) {
	          // invoke callback, update C
	          cdata[i][j] = inverse ? cf(b, x[i]) : cf(x[i], b);
	        } else {
	          // dense matrix value @ i, j
	          cdata[i][j] = b;
	        }
	      }
	    }

	    // return dense matrix
	    return new DenseMatrix({
	      data: cdata,
	      size: [rows, columns],
	      datatype: dt
	    });
	  };
	});
	return matAlgo10xSids;
}

var ArgumentsError = {};

var hasRequiredArgumentsError;

function requireArgumentsError () {
	if (hasRequiredArgumentsError) return ArgumentsError;
	hasRequiredArgumentsError = 1;

	Object.defineProperty(ArgumentsError, "__esModule", {
	  value: true
	});
	ArgumentsError.ArgumentsError = ArgumentsError$1;
	/**
	 * Create a syntax error with the message:
	 *     'Wrong number of arguments in function <fn> (<count> provided, <min>-<max> expected)'
	 * @param {string} fn     Function name
	 * @param {number} count  Actual argument count
	 * @param {number} min    Minimum required argument count
	 * @param {number} [max]  Maximum required argument count
	 * @extends Error
	 */
	function ArgumentsError$1(fn, count, min, max) {
	  if (!(this instanceof ArgumentsError$1)) {
	    throw new SyntaxError('Constructor must be called with the new operator');
	  }
	  this.fn = fn;
	  this.count = count;
	  this.min = min;
	  this.max = max;
	  this.message = 'Wrong number of arguments in function ' + fn + ' (' + count + ' provided, ' + min + (max !== undefined && max !== null ? '-' + max : '') + ' expected)';
	  this.stack = new Error().stack;
	}
	ArgumentsError$1.prototype = new Error();
	ArgumentsError$1.prototype.constructor = Error;
	ArgumentsError$1.prototype.name = 'ArgumentsError';
	ArgumentsError$1.prototype.isArgumentsError = true;
	return ArgumentsError;
}

var hasRequiredGcd$1;

function requireGcd$1 () {
	if (hasRequiredGcd$1) return gcd$1;
	hasRequiredGcd$1 = 1;

	Object.defineProperty(gcd$1, "__esModule", {
	  value: true
	});
	gcd$1.createGcd = void 0;
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _mod = requireMod$1();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo04xSidSid = requireMatAlgo04xSidSid();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _ArgumentsError = requireArgumentsError();
	const name = 'gcd';
	const dependencies = ['typed', 'config', 'round', 'matrix', 'equalScalar', 'zeros', 'BigNumber', 'DenseMatrix', 'concat'];
	const gcdTypes = 'number | BigNumber | Fraction | Matrix | Array';
	const gcdManyTypesSignature = `${gcdTypes}, ${gcdTypes}, ...${gcdTypes}`;
	function is1d(array) {
	  return !array.some(element => Array.isArray(element));
	}
	gcd$1.createGcd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    config,
	    round,
	    equalScalar,
	    zeros,
	    BigNumber,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const mod = (0, _mod.createMod)({
	    typed,
	    config,
	    round,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix,
	    concat
	  });
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo04xSidSid = (0, _matAlgo04xSidSid.createMatAlgo04xSidSid)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Calculate the greatest common divisor for two or more values or arrays.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.gcd(a, b)
	   *    math.gcd(a, b, c, ...)
	   *
	   * Examples:
	   *
	   *    math.gcd(8, 12)              // returns 4
	   *    math.gcd(-4, 6)              // returns 2
	   *    math.gcd(25, 15, -10)        // returns 5
	   *
	   *    math.gcd([8, -4], [12, 6])   // returns [4, 2]
	   *
	   * See also:
	   *
	   *    lcm, xgcd
	   *
	   * @param {... number | BigNumber | Fraction | Array | Matrix} args  Two or more integer numbers
	   * @return {number | BigNumber | Fraction | Array | Matrix}                           The greatest common divisor
	   */
	  return typed(name, {
	    'number, number': _gcdNumber,
	    'BigNumber, BigNumber': _gcdBigNumber,
	    'Fraction, Fraction': (x, y) => x.gcd(y)
	  }, matrixAlgorithmSuite({
	    SS: matAlgo04xSidSid,
	    DS: matAlgo01xDSid,
	    Ss: matAlgo10xSids
	  }), {
	    [gcdManyTypesSignature]: typed.referToSelf(self => (a, b, args) => {
	      let res = self(a, b);
	      for (let i = 0; i < args.length; i++) {
	        res = self(res, args[i]);
	      }
	      return res;
	    }),
	    Array: typed.referToSelf(self => array => {
	      if (array.length === 1 && Array.isArray(array[0]) && is1d(array[0])) {
	        return self(...array[0]);
	      }
	      if (is1d(array)) {
	        return self(...array);
	      }
	      throw new _ArgumentsError.ArgumentsError('gcd() supports only 1d matrices!');
	    }),
	    Matrix: typed.referToSelf(self => matrix => {
	      return self(matrix.toArray());
	    })
	  });

	  /**
	  * Calculate gcd for numbers
	  * @param {number} a
	  * @param {number} b
	  * @returns {number} Returns the greatest common denominator of a and b
	  * @private
	  */
	  function _gcdNumber(a, b) {
	    if (!(0, _number.isInteger)(a) || !(0, _number.isInteger)(b)) {
	      throw new Error('Parameters in function gcd must be integer numbers');
	    }

	    // https://en.wikipedia.org/wiki/Euclidean_algorithm
	    let r;
	    while (b !== 0) {
	      r = mod(a, b);
	      a = b;
	      b = r;
	    }
	    return a < 0 ? -a : a;
	  }

	  /**
	   * Calculate gcd for BigNumbers
	   * @param {BigNumber} a
	   * @param {BigNumber} b
	   * @returns {BigNumber} Returns greatest common denominator of a and b
	   * @private
	   */
	  function _gcdBigNumber(a, b) {
	    if (!a.isInt() || !b.isInt()) {
	      throw new Error('Parameters in function gcd must be integer numbers');
	    }

	    // https://en.wikipedia.org/wiki/Euclidean_algorithm
	    const zero = new BigNumber(0);
	    while (!b.isZero()) {
	      const r = mod(a, b);
	      a = b;
	      b = r;
	    }
	    return a.lt(zero) ? a.neg() : a;
	  }
	});
	return gcd$1;
}

var lcm$1 = {};

var matAlgo06xS0S0 = {};

var hasRequiredMatAlgo06xS0S0;

function requireMatAlgo06xS0S0 () {
	if (hasRequiredMatAlgo06xS0S0) return matAlgo06xS0S0;
	hasRequiredMatAlgo06xS0S0 = 1;

	Object.defineProperty(matAlgo06xS0S0, "__esModule", {
	  value: true
	});
	matAlgo06xS0S0.createMatAlgo06xS0S0 = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	var _collection = requireCollection();
	const name = 'matAlgo06xS0S0';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo06xS0S0.createMatAlgo06xS0S0 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix A and SparseMatrix B nonzero items and invokes the callback function f(Aij, Bij).
	   * Callback function invoked (Anz U Bnz) times, where Anz and Bnz are the nonzero elements in both matrices.
	   *
	   *
	   *          ┌  f(Aij, Bij)  ; A(i,j) !== 0 && B(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  0            ; otherwise
	   *
	   *
	   * @param {Matrix}   a                 The SparseMatrix instance (A)
	   * @param {Matrix}   b                 The SparseMatrix instance (B)
	   * @param {Function} callback          The f(Aij,Bij) operation to invoke
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97620294
	   */
	  return function matAlgo06xS0S0(a, b, callback) {
	    // sparse matrix arrays
	    const avalues = a._values;
	    const asize = a._size;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // sparse matrix arrays
	    const bvalues = b._values;
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cvalues = avalues && bvalues ? [] : undefined;
	    const cindex = [];
	    const cptr = [];

	    // workspaces
	    const x = cvalues ? [] : undefined;
	    // marks indicating we have a value in x for a given column
	    const w = [];
	    // marks indicating value in a given row has been updated
	    const u = [];

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // update cptr
	      cptr[j] = cindex.length;
	      // columns mark
	      const mark = j + 1;
	      // scatter the values of A(:,j) into workspace
	      (0, _collection.scatter)(a, j, w, x, u, mark, cindex, cf);
	      // scatter the values of B(:,j) into workspace
	      (0, _collection.scatter)(b, j, w, x, u, mark, cindex, cf);
	      // check we need to process values (non pattern matrix)
	      if (x) {
	        // initialize first index in j
	        let k = cptr[j];
	        // loop index in j
	        while (k < cindex.length) {
	          // row
	          const i = cindex[k];
	          // check function was invoked on current row (Aij !=0 && Bij != 0)
	          if (u[i] === mark) {
	            // value @ i
	            const v = x[i];
	            // check for zero value
	            if (!eq(v, zero)) {
	              // push value
	              cvalues.push(v);
	              // increment pointer
	              k++;
	            } else {
	              // remove value @ i, do not increment pointer
	              cindex.splice(k, 1);
	            }
	          } else {
	            // remove value @ i, do not increment pointer
	            cindex.splice(k, 1);
	          }
	        }
	      } else {
	        // initialize first index in j
	        let p = cptr[j];
	        // loop index in j
	        while (p < cindex.length) {
	          // row
	          const r = cindex[p];
	          // check function was invoked on current row (Aij !=0 && Bij != 0)
	          if (u[r] !== mark) {
	            // remove value @ i, do not increment pointer
	            cindex.splice(p, 1);
	          } else {
	            // increment pointer
	            p++;
	          }
	        }
	      }
	    }
	    // update cptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo06xS0S0;
}

var hasRequiredLcm$1;

function requireLcm$1 () {
	if (hasRequiredLcm$1) return lcm$1;
	hasRequiredLcm$1 = 1;

	Object.defineProperty(lcm$1, "__esModule", {
	  value: true
	});
	lcm$1.createLcm = void 0;
	var _factory = requireFactory$b();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo06xS0S = requireMatAlgo06xS0S0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'lcm';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'concat'];
	lcm$1.createLcm = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    concat
	  } = _ref;
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo06xS0S0 = (0, _matAlgo06xS0S.createMatAlgo06xS0S0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const lcmTypes = 'number | BigNumber | Fraction | Matrix | Array';
	  const lcmManySignature = {};
	  lcmManySignature[`${lcmTypes}, ${lcmTypes}, ...${lcmTypes}`] = typed.referToSelf(self => (a, b, args) => {
	    let res = self(a, b);
	    for (let i = 0; i < args.length; i++) {
	      res = self(res, args[i]);
	    }
	    return res;
	  });

	  /**
	   * Calculate the least common multiple for two or more values or arrays.
	   *
	   * lcm is defined as:
	   *
	   *     lcm(a, b) = abs(a * b) / gcd(a, b)
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.lcm(a, b)
	   *    math.lcm(a, b, c, ...)
	   *
	   * Examples:
	   *
	   *    math.lcm(4, 6)               // returns 12
	   *    math.lcm(6, 21)              // returns 42
	   *    math.lcm(6, 21, 5)           // returns 210
	   *
	   *    math.lcm([4, 6], [6, 21])    // returns [12, 42]
	   *
	   * See also:
	   *
	   *    gcd, xgcd
	   *
	   * @param {... number | BigNumber | Array | Matrix} args  Two or more integer numbers
	   * @return {number | BigNumber | Array | Matrix}                           The least common multiple
	   */
	  return typed(name, {
	    'number, number': _index.lcmNumber,
	    'BigNumber, BigNumber': _lcmBigNumber,
	    'Fraction, Fraction': (x, y) => x.lcm(y)
	  }, matrixAlgorithmSuite({
	    SS: matAlgo06xS0S0,
	    DS: matAlgo02xDS0,
	    Ss: matAlgo11xS0s
	  }), lcmManySignature);

	  /**
	   * Calculate lcm for two BigNumbers
	   * @param {BigNumber} a
	   * @param {BigNumber} b
	   * @returns {BigNumber} Returns the least common multiple of a and b
	   * @private
	   */
	  function _lcmBigNumber(a, b) {
	    if (!a.isInt() || !b.isInt()) {
	      throw new Error('Parameters in function lcm must be integer numbers');
	    }
	    if (a.isZero()) {
	      return a;
	    }
	    if (b.isZero()) {
	      return b;
	    }

	    // https://en.wikipedia.org/wiki/Euclidean_algorithm
	    // evaluate lcm here inline to reduce overhead
	    const prod = a.times(b);
	    while (!b.isZero()) {
	      const t = b;
	      b = a.mod(t);
	      a = t;
	    }
	    return prod.div(a).abs();
	  }
	});
	return lcm$1;
}

var log10$1 = {};

var bigint$1 = {};

var hasRequiredBigint$1;

function requireBigint$1 () {
	if (hasRequiredBigint$1) return bigint$1;
	hasRequiredBigint$1 = 1;

	Object.defineProperty(bigint$1, "__esModule", {
	  value: true
	});
	bigint$1.promoteLogarithm = promoteLogarithm;
	/**
	 * Build a bigint logarithm function from a number logarithm,
	 * still returning a number. The idea is that 15 hexadecimal digits
	 * (60 bits) saturates the mantissa of the log, and each additional hex
	 * digit effectively just adds the log of 16 to the resulting value. So
	 * convert the most significant 15 hex digits to a number and take its
	 * log, and then add the log of 16 for each additional hex digit that
	 * was in the bigint.
	 * For negative numbers (complex logarithms), following the bignum
	 * implementation, it just downgrades to number and uses the complex result.
	 * @param {number} log16  the log of 16
	 * @param {(number) -> number} numberLog  the logarithm function for numbers
	 * @param {ConfigurationObject} config  the mathjs configuration
	 * @param {(number) -> Complex} cplx  the associated Complex log
	 * @returns {(bigint) -> number}   the corresponding logarithm for bigints
	 */
	function promoteLogarithm(log16, numberLog, config, cplx) {
	  return function (b) {
	    if (b > 0 || config.predictable) {
	      if (b <= 0) return NaN;
	      const s = b.toString(16);
	      const s15 = s.substring(0, 15);
	      return log16 * (s.length - s15.length) + numberLog(Number('0x' + s15));
	    }
	    return cplx(b.toNumber());
	  };
	}
	return bigint$1;
}

var hasRequiredLog10$1;

function requireLog10$1 () {
	if (hasRequiredLog10$1) return log10$1;
	hasRequiredLog10$1 = 1;

	Object.defineProperty(log10$1, "__esModule", {
	  value: true
	});
	log10$1.createLog10 = void 0;
	var _index = requireNumber$2();
	var _bigint = requireBigint$1();
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'log10';
	const dependencies = ['typed', 'config', 'Complex'];
	const log16 = (0, _index.log10Number)(16);
	log10$1.createLog10 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the 10-base logarithm of a value. This is the same as calculating `log(x, 10)`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.log10(x)
	   *
	   * Examples:
	   *
	   *    math.log10(0.00001)            // returns -5
	   *    math.log10(10000)              // returns 4
	   *    math.log(10000) / math.log(10) // returns 4
	   *    math.pow(10, 4)                // returns 10000
	   *
	   * See also:
	   *
	   *    exp, log, log1p, log2
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix} x
	   *            Value for which to calculate the logarithm.
	   * @return {number | BigNumber | Complex | Array | Matrix}
	   *            Returns the 10-base logarithm of `x`
	   */

	  function complexLog(c) {
	    return c.log().div(Math.LN10);
	  }
	  function complexLogNumber(x) {
	    return complexLog(new Complex(x, 0));
	  }
	  return typed(name, {
	    number: function (x) {
	      if (x >= 0 || config.predictable) {
	        return (0, _index.log10Number)(x);
	      } else {
	        // negative value -> complex value computation
	        return complexLogNumber(x);
	      }
	    },
	    bigint: (0, _bigint.promoteLogarithm)(log16, _index.log10Number, config, complexLogNumber),
	    Complex: complexLog,
	    BigNumber: function (x) {
	      if (!x.isNegative() || config.predictable) {
	        return x.log();
	      } else {
	        // downgrade to number, return Complex valued result
	        return complexLogNumber(x.toNumber());
	      }
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return log10$1;
}

var log2$1 = {};

var hasRequiredLog2$1;

function requireLog2$1 () {
	if (hasRequiredLog2$1) return log2$1;
	hasRequiredLog2$1 = 1;

	Object.defineProperty(log2$1, "__esModule", {
	  value: true
	});
	log2$1.createLog2 = void 0;
	var _index = requireNumber$2();
	var _bigint = requireBigint$1();
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'log2';
	const dependencies = ['typed', 'config', 'Complex'];
	log2$1.createLog2 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the 2-base of a value. This is the same as calculating `log(x, 2)`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.log2(x)
	   *
	   * Examples:
	   *
	   *    math.log2(0.03125)           // returns -5
	   *    math.log2(16)                // returns 4
	   *    math.log2(16) / math.log2(2) // returns 4
	   *    math.pow(2, 4)               // returns 16
	   *
	   * See also:
	   *
	   *    exp, log, log1p, log10
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix} x
	   *            Value for which to calculate the logarithm.
	   * @return {number | BigNumber | Complex | Array | Matrix}
	   *            Returns the 2-base logarithm of `x`
	   */
	  function complexLog2Number(x) {
	    return _log2Complex(new Complex(x, 0));
	  }
	  return typed(name, {
	    number: function (x) {
	      if (x >= 0 || config.predictable) {
	        return (0, _index.log2Number)(x);
	      } else {
	        // negative value -> complex value computation
	        return complexLog2Number(x);
	      }
	    },
	    bigint: (0, _bigint.promoteLogarithm)(4, _index.log2Number, config, complexLog2Number),
	    Complex: _log2Complex,
	    BigNumber: function (x) {
	      if (!x.isNegative() || config.predictable) {
	        return x.log(2);
	      } else {
	        // downgrade to number, return Complex valued result
	        return complexLog2Number(x.toNumber());
	      }
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });

	  /**
	   * Calculate log2 for a complex value
	   * @param {Complex} x
	   * @returns {Complex}
	   * @private
	   */
	  function _log2Complex(x) {
	    const newX = Math.sqrt(x.re * x.re + x.im * x.im);
	    return new Complex(Math.log2 ? Math.log2(newX) : Math.log(newX) / Math.LN2, Math.atan2(x.im, x.re) / Math.LN2);
	  }
	});
	return log2$1;
}

var multiplyScalar = {};

var hasRequiredMultiplyScalar;

function requireMultiplyScalar () {
	if (hasRequiredMultiplyScalar) return multiplyScalar;
	hasRequiredMultiplyScalar = 1;

	Object.defineProperty(multiplyScalar, "__esModule", {
	  value: true
	});
	multiplyScalar.createMultiplyScalar = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'multiplyScalar';
	const dependencies = ['typed'];
	multiplyScalar.createMultiplyScalar = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Multiply two scalar values, `x * y`.
	   * This function is meant for internal use: it is used by the public function
	   * `multiply`
	   *
	   * This function does not support collections (Array or Matrix).
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit} x   First value to multiply
	   * @param  {number | BigNumber | bigint | Fraction | Complex} y          Second value to multiply
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit}     Multiplication of `x` and `y`
	   * @private
	   */
	  return typed('multiplyScalar', {
	    'number, number': _index.multiplyNumber,
	    'Complex, Complex': function (x, y) {
	      return x.mul(y);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return x.times(y);
	    },
	    'bigint, bigint': function (x, y) {
	      return x * y;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return x.mul(y);
	    },
	    'number | Fraction | BigNumber | Complex, Unit': (x, y) => y.multiply(x),
	    'Unit, number | Fraction | BigNumber | Complex | Unit': (x, y) => x.multiply(y)
	  });
	});
	return multiplyScalar;
}

var multiply$1 = {};

var hasRequiredMultiply$1;

function requireMultiply$1 () {
	if (hasRequiredMultiply$1) return multiply$1;
	hasRequiredMultiply$1 = 1;

	Object.defineProperty(multiply$1, "__esModule", {
	  value: true
	});
	multiply$1.createMultiply = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _array = requireArray();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	const name = 'multiply';
	const dependencies = ['typed', 'matrix', 'addScalar', 'multiplyScalar', 'equalScalar', 'dot'];
	multiply$1.createMultiply = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    addScalar,
	    multiplyScalar,
	    equalScalar,
	    dot
	  } = _ref;
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  function _validateMatrixDimensions(size1, size2) {
	    // check left operand dimensions
	    switch (size1.length) {
	      case 1:
	        // check size2
	        switch (size2.length) {
	          case 1:
	            // Vector x Vector
	            if (size1[0] !== size2[0]) {
	              // throw error
	              throw new RangeError('Dimension mismatch in multiplication. Vectors must have the same length');
	            }
	            break;
	          case 2:
	            // Vector x Matrix
	            if (size1[0] !== size2[0]) {
	              // throw error
	              throw new RangeError('Dimension mismatch in multiplication. Vector length (' + size1[0] + ') must match Matrix rows (' + size2[0] + ')');
	            }
	            break;
	          default:
	            throw new Error('Can only multiply a 1 or 2 dimensional matrix (Matrix B has ' + size2.length + ' dimensions)');
	        }
	        break;
	      case 2:
	        // check size2
	        switch (size2.length) {
	          case 1:
	            // Matrix x Vector
	            if (size1[1] !== size2[0]) {
	              // throw error
	              throw new RangeError('Dimension mismatch in multiplication. Matrix columns (' + size1[1] + ') must match Vector length (' + size2[0] + ')');
	            }
	            break;
	          case 2:
	            // Matrix x Matrix
	            if (size1[1] !== size2[0]) {
	              // throw error
	              throw new RangeError('Dimension mismatch in multiplication. Matrix A columns (' + size1[1] + ') must match Matrix B rows (' + size2[0] + ')');
	            }
	            break;
	          default:
	            throw new Error('Can only multiply a 1 or 2 dimensional matrix (Matrix B has ' + size2.length + ' dimensions)');
	        }
	        break;
	      default:
	        throw new Error('Can only multiply a 1 or 2 dimensional matrix (Matrix A has ' + size1.length + ' dimensions)');
	    }
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            Dense Vector   (N)
	   * @param {Matrix} b            Dense Vector   (N)
	   *
	   * @return {number}             Scalar value
	   */
	  function _multiplyVectorVector(a, b, n) {
	    // check empty vector
	    if (n === 0) {
	      throw new Error('Cannot multiply two empty vectors');
	    }
	    return dot(a, b);
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            Dense Vector   (M)
	   * @param {Matrix} b            Matrix         (MxN)
	   *
	   * @return {Matrix}             Dense Vector   (N)
	   */
	  function _multiplyVectorMatrix(a, b) {
	    // process storage
	    if (b.storage() !== 'dense') {
	      throw new Error('Support for SparseMatrix not implemented');
	    }
	    return _multiplyVectorDenseMatrix(a, b);
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            Dense Vector   (M)
	   * @param {Matrix} b            Dense Matrix   (MxN)
	   *
	   * @return {Matrix}             Dense Vector   (N)
	   */
	  function _multiplyVectorDenseMatrix(a, b) {
	    // a dense
	    const adata = a._data;
	    const asize = a._size;
	    const adt = a._datatype || a.getDataType();
	    // b dense
	    const bdata = b._data;
	    const bsize = b._size;
	    const bdt = b._datatype || b.getDataType();
	    // rows & columns
	    const alength = asize[0];
	    const bcolumns = bsize[1];

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	    }

	    // result
	    const c = [];

	    // loop matrix columns
	    for (let j = 0; j < bcolumns; j++) {
	      // sum (do not initialize it with zero)
	      let sum = mf(adata[0], bdata[0][j]);
	      // loop vector
	      for (let i = 1; i < alength; i++) {
	        // multiply & accumulate
	        sum = af(sum, mf(adata[i], bdata[i][j]));
	      }
	      c[j] = sum;
	    }

	    // return matrix
	    return a.createDenseMatrix({
	      data: c,
	      size: [bcolumns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            Matrix         (MxN)
	   * @param {Matrix} b            Dense Vector   (N)
	   *
	   * @return {Matrix}             Dense Vector   (M)
	   */
	  const _multiplyMatrixVector = typed('_multiplyMatrixVector', {
	    'DenseMatrix, any': _multiplyDenseMatrixVector,
	    'SparseMatrix, any': _multiplySparseMatrixVector
	  });

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            Matrix         (MxN)
	   * @param {Matrix} b            Matrix         (NxC)
	   *
	   * @return {Matrix}             Matrix         (MxC)
	   */
	  const _multiplyMatrixMatrix = typed('_multiplyMatrixMatrix', {
	    'DenseMatrix, DenseMatrix': _multiplyDenseMatrixDenseMatrix,
	    'DenseMatrix, SparseMatrix': _multiplyDenseMatrixSparseMatrix,
	    'SparseMatrix, DenseMatrix': _multiplySparseMatrixDenseMatrix,
	    'SparseMatrix, SparseMatrix': _multiplySparseMatrixSparseMatrix
	  });

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            DenseMatrix  (MxN)
	   * @param {Matrix} b            Dense Vector (N)
	   *
	   * @return {Matrix}             Dense Vector (M)
	   */
	  function _multiplyDenseMatrixVector(a, b) {
	    // a dense
	    const adata = a._data;
	    const asize = a._size;
	    const adt = a._datatype || a.getDataType();
	    // b dense
	    const bdata = b._data;
	    const bdt = b._datatype || b.getDataType();
	    // rows & columns
	    const arows = asize[0];
	    const acolumns = asize[1];

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	    }

	    // result
	    const c = [];

	    // loop matrix a rows
	    for (let i = 0; i < arows; i++) {
	      // current row
	      const row = adata[i];
	      // sum (do not initialize it with zero)
	      let sum = mf(row[0], bdata[0]);
	      // loop matrix a columns
	      for (let j = 1; j < acolumns; j++) {
	        // multiply & accumulate
	        sum = af(sum, mf(row[j], bdata[j]));
	      }
	      c[i] = sum;
	    }

	    // return matrix
	    return a.createDenseMatrix({
	      data: c,
	      size: [arows],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            DenseMatrix    (MxN)
	   * @param {Matrix} b            DenseMatrix    (NxC)
	   *
	   * @return {Matrix}             DenseMatrix    (MxC)
	   */
	  function _multiplyDenseMatrixDenseMatrix(a, b) {
	    // getDataType()
	    // a dense
	    const adata = a._data;
	    const asize = a._size;
	    const adt = a._datatype || a.getDataType();
	    // b dense
	    const bdata = b._data;
	    const bsize = b._size;
	    const bdt = b._datatype || b.getDataType();
	    // rows & columns
	    const arows = asize[0];
	    const acolumns = asize[1];
	    const bcolumns = bsize[1];

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	    }

	    // result
	    const c = [];

	    // loop matrix a rows
	    for (let i = 0; i < arows; i++) {
	      // current row
	      const row = adata[i];
	      // initialize row array
	      c[i] = [];
	      // loop matrix b columns
	      for (let j = 0; j < bcolumns; j++) {
	        // sum (avoid initializing sum to zero)
	        let sum = mf(row[0], bdata[0][j]);
	        // loop matrix a columns
	        for (let x = 1; x < acolumns; x++) {
	          // multiply & accumulate
	          sum = af(sum, mf(row[x], bdata[x][j]));
	        }
	        c[i][j] = sum;
	      }
	    }

	    // return matrix
	    return a.createDenseMatrix({
	      data: c,
	      size: [arows, bcolumns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            DenseMatrix    (MxN)
	   * @param {Matrix} b            SparseMatrix   (NxC)
	   *
	   * @return {Matrix}             SparseMatrix   (MxC)
	   */
	  function _multiplyDenseMatrixSparseMatrix(a, b) {
	    // a dense
	    const adata = a._data;
	    const asize = a._size;
	    const adt = a._datatype || a.getDataType();
	    // b sparse
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();
	    // validate b matrix
	    if (!bvalues) {
	      throw new Error('Cannot multiply Dense Matrix times Pattern only Matrix');
	    }
	    // rows & columns
	    const arows = asize[0];
	    const bcolumns = bsize[1];

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;
	    // equalScalar signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	    }

	    // result
	    const cvalues = [];
	    const cindex = [];
	    const cptr = [];
	    // c matrix
	    const c = b.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [arows, bcolumns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });

	    // loop b columns
	    for (let jb = 0; jb < bcolumns; jb++) {
	      // update ptr
	      cptr[jb] = cindex.length;
	      // indeces in column jb
	      const kb0 = bptr[jb];
	      const kb1 = bptr[jb + 1];
	      // do not process column jb if no data exists
	      if (kb1 > kb0) {
	        // last row mark processed
	        let last = 0;
	        // loop a rows
	        for (let i = 0; i < arows; i++) {
	          // column mark
	          const mark = i + 1;
	          // C[i, jb]
	          let cij;
	          // values in b column j
	          for (let kb = kb0; kb < kb1; kb++) {
	            // row
	            const ib = bindex[kb];
	            // check value has been initialized
	            if (last !== mark) {
	              // first value in column jb
	              cij = mf(adata[i][ib], bvalues[kb]);
	              // update mark
	              last = mark;
	            } else {
	              // accumulate value
	              cij = af(cij, mf(adata[i][ib], bvalues[kb]));
	            }
	          }
	          // check column has been processed and value != 0
	          if (last === mark && !eq(cij, zero)) {
	            // push row & value
	            cindex.push(i);
	            cvalues.push(cij);
	          }
	        }
	      }
	    }
	    // update ptr
	    cptr[bcolumns] = cindex.length;

	    // return sparse matrix
	    return c;
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            SparseMatrix    (MxN)
	   * @param {Matrix} b            Dense Vector (N)
	   *
	   * @return {Matrix}             SparseMatrix    (M, 1)
	   */
	  function _multiplySparseMatrixVector(a, b) {
	    // a sparse
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // validate a matrix
	    if (!avalues) {
	      throw new Error('Cannot multiply Pattern only Matrix times Dense Matrix');
	    }
	    // b dense
	    const bdata = b._data;
	    const bdt = b._datatype || b.getDataType();
	    // rows & columns
	    const arows = a._size[0];
	    const brows = b._size[0];
	    // result
	    const cvalues = [];
	    const cindex = [];
	    const cptr = [];

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;
	    // equalScalar signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	    }

	    // workspace
	    const x = [];
	    // vector with marks indicating a value x[i] exists in a given column
	    const w = [];

	    // update ptr
	    cptr[0] = 0;
	    // rows in b
	    for (let ib = 0; ib < brows; ib++) {
	      // b[ib]
	      const vbi = bdata[ib];
	      // check b[ib] != 0, avoid loops
	      if (!eq(vbi, zero)) {
	        // A values & index in ib column
	        for (let ka0 = aptr[ib], ka1 = aptr[ib + 1], ka = ka0; ka < ka1; ka++) {
	          // a row
	          const ia = aindex[ka];
	          // check value exists in current j
	          if (!w[ia]) {
	            // ia is new entry in j
	            w[ia] = true;
	            // add i to pattern of C
	            cindex.push(ia);
	            // x(ia) = A
	            x[ia] = mf(vbi, avalues[ka]);
	          } else {
	            // i exists in C already
	            x[ia] = af(x[ia], mf(vbi, avalues[ka]));
	          }
	        }
	      }
	    }
	    // copy values from x to column jb of c
	    for (let p1 = cindex.length, p = 0; p < p1; p++) {
	      // row
	      const ic = cindex[p];
	      // copy value
	      cvalues[p] = x[ic];
	    }
	    // update ptr
	    cptr[1] = cindex.length;

	    // matrix to return
	    return a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [arows, 1],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            SparseMatrix      (MxN)
	   * @param {Matrix} b            DenseMatrix       (NxC)
	   *
	   * @return {Matrix}             SparseMatrix      (MxC)
	   */
	  function _multiplySparseMatrixDenseMatrix(a, b) {
	    // a sparse
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // validate a matrix
	    if (!avalues) {
	      throw new Error('Cannot multiply Pattern only Matrix times Dense Matrix');
	    }
	    // b dense
	    const bdata = b._data;
	    const bdt = b._datatype || b.getDataType();
	    // rows & columns
	    const arows = a._size[0];
	    const brows = b._size[0];
	    const bcolumns = b._size[1];

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;
	    // equalScalar signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	    }

	    // result
	    const cvalues = [];
	    const cindex = [];
	    const cptr = [];
	    // c matrix
	    const c = a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [arows, bcolumns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });

	    // workspace
	    const x = [];
	    // vector with marks indicating a value x[i] exists in a given column
	    const w = [];

	    // loop b columns
	    for (let jb = 0; jb < bcolumns; jb++) {
	      // update ptr
	      cptr[jb] = cindex.length;
	      // mark in workspace for current column
	      const mark = jb + 1;
	      // rows in jb
	      for (let ib = 0; ib < brows; ib++) {
	        // b[ib, jb]
	        const vbij = bdata[ib][jb];
	        // check b[ib, jb] != 0, avoid loops
	        if (!eq(vbij, zero)) {
	          // A values & index in ib column
	          for (let ka0 = aptr[ib], ka1 = aptr[ib + 1], ka = ka0; ka < ka1; ka++) {
	            // a row
	            const ia = aindex[ka];
	            // check value exists in current j
	            if (w[ia] !== mark) {
	              // ia is new entry in j
	              w[ia] = mark;
	              // add i to pattern of C
	              cindex.push(ia);
	              // x(ia) = A
	              x[ia] = mf(vbij, avalues[ka]);
	            } else {
	              // i exists in C already
	              x[ia] = af(x[ia], mf(vbij, avalues[ka]));
	            }
	          }
	        }
	      }
	      // copy values from x to column jb of c
	      for (let p0 = cptr[jb], p1 = cindex.length, p = p0; p < p1; p++) {
	        // row
	        const ic = cindex[p];
	        // copy value
	        cvalues[p] = x[ic];
	      }
	    }
	    // update ptr
	    cptr[bcolumns] = cindex.length;

	    // return sparse matrix
	    return c;
	  }

	  /**
	   * C = A * B
	   *
	   * @param {Matrix} a            SparseMatrix      (MxN)
	   * @param {Matrix} b            SparseMatrix      (NxC)
	   *
	   * @return {Matrix}             SparseMatrix      (MxC)
	   */
	  function _multiplySparseMatrixSparseMatrix(a, b) {
	    // a sparse
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // b sparse
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // rows & columns
	    const arows = a._size[0];
	    const bcolumns = b._size[1];
	    // flag indicating both matrices (a & b) contain data
	    const values = avalues && bvalues;

	    // datatype
	    let dt;
	    // addScalar signature to use
	    let af = addScalar;
	    // multiplyScalar signature to use
	    let mf = multiplyScalar;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signatures that matches (dt, dt)
	      af = typed.find(addScalar, [dt, dt]);
	      mf = typed.find(multiplyScalar, [dt, dt]);
	    }

	    // result
	    const cvalues = values ? [] : undefined;
	    const cindex = [];
	    const cptr = [];
	    // c matrix
	    const c = a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [arows, bcolumns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });

	    // workspace
	    const x = values ? [] : undefined;
	    // vector with marks indicating a value x[i] exists in a given column
	    const w = [];
	    // variables
	    let ka, ka0, ka1, kb, kb0, kb1, ia, ib;
	    // loop b columns
	    for (let jb = 0; jb < bcolumns; jb++) {
	      // update ptr
	      cptr[jb] = cindex.length;
	      // mark in workspace for current column
	      const mark = jb + 1;
	      // B values & index in j
	      for (kb0 = bptr[jb], kb1 = bptr[jb + 1], kb = kb0; kb < kb1; kb++) {
	        // b row
	        ib = bindex[kb];
	        // check we need to process values
	        if (values) {
	          // loop values in a[:,ib]
	          for (ka0 = aptr[ib], ka1 = aptr[ib + 1], ka = ka0; ka < ka1; ka++) {
	            // row
	            ia = aindex[ka];
	            // check value exists in current j
	            if (w[ia] !== mark) {
	              // ia is new entry in j
	              w[ia] = mark;
	              // add i to pattern of C
	              cindex.push(ia);
	              // x(ia) = A
	              x[ia] = mf(bvalues[kb], avalues[ka]);
	            } else {
	              // i exists in C already
	              x[ia] = af(x[ia], mf(bvalues[kb], avalues[ka]));
	            }
	          }
	        } else {
	          // loop values in a[:,ib]
	          for (ka0 = aptr[ib], ka1 = aptr[ib + 1], ka = ka0; ka < ka1; ka++) {
	            // row
	            ia = aindex[ka];
	            // check value exists in current j
	            if (w[ia] !== mark) {
	              // ia is new entry in j
	              w[ia] = mark;
	              // add i to pattern of C
	              cindex.push(ia);
	            }
	          }
	        }
	      }
	      // check we need to process matrix values (pattern matrix)
	      if (values) {
	        // copy values from x to column jb of c
	        for (let p0 = cptr[jb], p1 = cindex.length, p = p0; p < p1; p++) {
	          // row
	          const ic = cindex[p];
	          // copy value
	          cvalues[p] = x[ic];
	        }
	      }
	    }
	    // update ptr
	    cptr[bcolumns] = cindex.length;

	    // return sparse matrix
	    return c;
	  }

	  /**
	   * Multiply two or more values, `x * y`.
	   * For matrices, the matrix product is calculated.
	   *
	   * Syntax:
	   *
	   *    math.multiply(x, y)
	   *    math.multiply(x, y, z, ...)
	   *
	   * Examples:
	   *
	   *    math.multiply(4, 5.2)        // returns number 20.8
	   *    math.multiply(2, 3, 4)       // returns number 24
	   *
	   *    const a = math.complex(2, 3)
	   *    const b = math.complex(4, 1)
	   *    math.multiply(a, b)          // returns Complex 5 + 14i
	   *
	   *    const c = [[1, 2], [4, 3]]
	   *    const d = [[1, 2, 3], [3, -4, 7]]
	   *    math.multiply(c, d)          // returns Array [[7, -6, 17], [13, -4, 33]]
	   *
	   *    const e = math.unit('2.1 km')
	   *    math.multiply(3, e)          // returns Unit 6.3 km
	   *
	   * See also:
	   *
	   *    divide, prod, cross, dot
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} x First value to multiply
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} y Second value to multiply
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} Multiplication of `x` and `y`
	   */
	  return typed(name, multiplyScalar, {
	    // we extend the signatures of multiplyScalar with signatures dealing with matrices

	    'Array, Array': typed.referTo('Matrix, Matrix', selfMM => (x, y) => {
	      // check dimensions
	      _validateMatrixDimensions((0, _array.arraySize)(x), (0, _array.arraySize)(y));

	      // use dense matrix implementation
	      const m = selfMM(matrix(x), matrix(y));
	      // return array or scalar
	      return (0, _is.isMatrix)(m) ? m.valueOf() : m;
	    }),
	    'Matrix, Matrix': function (x, y) {
	      // dimensions
	      const xsize = x.size();
	      const ysize = y.size();

	      // check dimensions
	      _validateMatrixDimensions(xsize, ysize);

	      // process dimensions
	      if (xsize.length === 1) {
	        // process y dimensions
	        if (ysize.length === 1) {
	          // Vector * Vector
	          return _multiplyVectorVector(x, y, xsize[0]);
	        }
	        // Vector * Matrix
	        return _multiplyVectorMatrix(x, y);
	      }
	      // process y dimensions
	      if (ysize.length === 1) {
	        // Matrix * Vector
	        return _multiplyMatrixVector(x, y);
	      }
	      // Matrix * Matrix
	      return _multiplyMatrixMatrix(x, y);
	    },
	    'Matrix, Array': typed.referTo('Matrix,Matrix', selfMM => (x, y) => selfMM(x, matrix(y))),
	    'Array, Matrix': typed.referToSelf(self => (x, y) => {
	      // use Matrix * Matrix implementation
	      return self(matrix(x, y.storage()), y);
	    }),
	    'SparseMatrix, any': function (x, y) {
	      return matAlgo11xS0s(x, y, multiplyScalar, false);
	    },
	    'DenseMatrix, any': function (x, y) {
	      return matAlgo14xDs(x, y, multiplyScalar, false);
	    },
	    'any, SparseMatrix': function (x, y) {
	      return matAlgo11xS0s(y, x, multiplyScalar, true);
	    },
	    'any, DenseMatrix': function (x, y) {
	      return matAlgo14xDs(y, x, multiplyScalar, true);
	    },
	    'Array, any': function (x, y) {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(x), y, multiplyScalar, false).valueOf();
	    },
	    'any, Array': function (x, y) {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(y), x, multiplyScalar, true).valueOf();
	    },
	    'any, any': multiplyScalar,
	    'any, any, ...any': typed.referToSelf(self => (x, y, rest) => {
	      let result = self(x, y);
	      for (let i = 0; i < rest.length; i++) {
	        result = self(result, rest[i]);
	      }
	      return result;
	    })
	  });
	});
	return multiply$1;
}

var nthRoot$1 = {};

var hasRequiredNthRoot$1;

function requireNthRoot$1 () {
	if (hasRequiredNthRoot$1) return nthRoot$1;
	hasRequiredNthRoot$1 = 1;

	Object.defineProperty(nthRoot$1, "__esModule", {
	  value: true
	});
	nthRoot$1.createNthRootNumber = nthRoot$1.createNthRoot = void 0;
	var _factory = requireFactory$b();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo06xS0S = requireMatAlgo06xS0S0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'nthRoot';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'BigNumber', 'concat'];
	nthRoot$1.createNthRoot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    BigNumber,
	    concat
	  } = _ref;
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo06xS0S0 = (0, _matAlgo06xS0S.createMatAlgo06xS0S0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Calculate the nth root of a value.
	   * The principal nth root of a positive real number A, is the positive real
	   * solution of the equation
	   *
	   *     x^root = A
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *     math.nthRoot(a)
	   *     math.nthRoot(a, root)
	   *
	   * Examples:
	   *
	   *     math.nthRoot(9, 2)    // returns 3
	   *     math.sqrt(9)          // returns 3
	   *     math.nthRoot(64, 3)   // returns 4
	   *
	   * See also:
	   *
	   *     sqrt, pow
	   *
	   * History:
	   *
	   *     v11.6  Support matrix broadcasting
	   *     v2     Handle Complex
	   *     v1.1   Created
	   *
	   * @param {number | BigNumber | Array | Matrix | Complex} a
	   *              Value for which to calculate the nth root
	   * @param {number | BigNumber} [root=2]    The root.
	   * @return {number | Complex | Array | Matrix} Returns the nth root of `a`
	   */
	  function complexErr() {
	    throw new Error('Complex number not supported in function nthRoot. Use nthRoots instead.');
	  }
	  return typed(name, {
	    number: _index.nthRootNumber,
	    'number, number': _index.nthRootNumber,
	    BigNumber: x => _bigNthRoot(x, new BigNumber(2)),
	    'BigNumber, BigNumber': _bigNthRoot,
	    Complex: complexErr,
	    'Complex, number': complexErr,
	    Array: typed.referTo('DenseMatrix,number', selfDn => x => selfDn(matrix(x), 2).valueOf()),
	    DenseMatrix: typed.referTo('DenseMatrix,number', selfDn => x => selfDn(x, 2)),
	    SparseMatrix: typed.referTo('SparseMatrix,number', selfSn => x => selfSn(x, 2)),
	    'SparseMatrix, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // density must be one (no zeros in matrix)
	      if (y.density() === 1) {
	        // sparse + sparse
	        return matAlgo06xS0S0(x, y, self);
	      } else {
	        // throw exception
	        throw new Error('Root must be non-zero');
	      }
	    }),
	    'DenseMatrix, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // density must be one (no zeros in matrix)
	      if (y.density() === 1) {
	        // dense + sparse
	        return matAlgo01xDSid(x, y, self, false);
	      } else {
	        // throw exception
	        throw new Error('Root must be non-zero');
	      }
	    }),
	    'Array, SparseMatrix': typed.referTo('DenseMatrix,SparseMatrix', selfDS => (x, y) => selfDS(matrix(x), y)),
	    'number | BigNumber, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // density must be one (no zeros in matrix)
	      if (y.density() === 1) {
	        // sparse - scalar
	        return matAlgo11xS0s(y, x, self, true);
	      } else {
	        // throw exception
	        throw new Error('Root must be non-zero');
	      }
	    })
	  }, matrixAlgorithmSuite({
	    scalar: 'number | BigNumber',
	    SD: matAlgo02xDS0,
	    Ss: matAlgo11xS0s,
	    sS: false
	  }));

	  /**
	   * Calculate the nth root of a for BigNumbers, solve x^root == a
	   * https://rosettacode.org/wiki/Nth_root#JavaScript
	   * @param {BigNumber} a
	   * @param {BigNumber} root
	   * @private
	   */
	  function _bigNthRoot(a, root) {
	    const precision = BigNumber.precision;
	    const Big = BigNumber.clone({
	      precision: precision + 2
	    });
	    const zero = new BigNumber(0);
	    const one = new Big(1);
	    const inv = root.isNegative();
	    if (inv) {
	      root = root.neg();
	    }
	    if (root.isZero()) {
	      throw new Error('Root must be non-zero');
	    }
	    if (a.isNegative() && !root.abs().mod(2).equals(1)) {
	      throw new Error('Root must be odd when a is negative.');
	    }

	    // edge cases zero and infinity
	    if (a.isZero()) {
	      return inv ? new Big(Infinity) : 0;
	    }
	    if (!a.isFinite()) {
	      return inv ? zero : a;
	    }
	    let x = a.abs().pow(one.div(root));
	    // If a < 0, we require that root is an odd integer,
	    // so (-1) ^ (1/root) = -1
	    x = a.isNeg() ? x.neg() : x;
	    return new BigNumber((inv ? one.div(x) : x).toPrecision(precision));
	  }
	});
	nthRoot$1.createNthRootNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed'], _ref2 => {
	  let {
	    typed
	  } = _ref2;
	  return typed(name, {
	    number: _index.nthRootNumber,
	    'number, number': _index.nthRootNumber
	  });
	});
	return nthRoot$1;
}

var sign$1 = {};

var hasRequiredSign$1;

function requireSign$1 () {
	if (hasRequiredSign$1) return sign$1;
	hasRequiredSign$1 = 1;

	Object.defineProperty(sign$1, "__esModule", {
	  value: true
	});
	sign$1.createSign = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _index = requireNumber$2();
	const name = 'sign';
	const dependencies = ['typed', 'BigNumber', 'Fraction', 'complex'];
	sign$1.createSign = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber,
	    complex,
	    Fraction
	  } = _ref;
	  /**
	   * Compute the sign of a value. The sign of a value x is:
	   *
	   * -  1 when x > 0
	   * - -1 when x < 0
	   * -  0 when x == 0
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.sign(x)
	   *
	   * Examples:
	   *
	   *    math.sign(3.5)               // returns 1
	   *    math.sign(-4.2)              // returns -1
	   *    math.sign(0)                 // returns 0
	   *
	   *    math.sign([3, 5, -2, 0, 2])  // returns [1, 1, -1, 0, 1]
	   *
	   * See also:
	   *
	   *    abs
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Array | Matrix | Unit} x
	   *            The number for which to determine the sign
	   * @return {number | BigNumber | bigint | Fraction | Complex | Array | Matrix | Unit}
	   *            The sign of `x`
	   */
	  return typed(name, {
	    number: _index.signNumber,
	    Complex: function (x) {
	      return x.im === 0 ? complex((0, _index.signNumber)(x.re)) : x.sign();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(x.cmp(0));
	    },
	    bigint: function (x) {
	      return x > 0n ? 1n : x < 0n ? -1n : 0n;
	    },
	    Fraction: function (x) {
	      return x.n === 0n ? new Fraction(0) : new Fraction(x.s);
	    },
	    // deep map collection, skip zeros since sign(0) = 0
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self, true)),
	    Unit: typed.referToSelf(self => x => {
	      if (!x._isDerived() && x.units[0].unit.offset !== 0) {
	        throw new TypeError('sign is ambiguous for units with offset');
	      }
	      return typed.find(self, x.valueType())(x.value);
	    })
	  });
	});
	return sign$1;
}

var sqrt$1 = {};

var hasRequiredSqrt$1;

function requireSqrt$1 () {
	if (hasRequiredSqrt$1) return sqrt$1;
	hasRequiredSqrt$1 = 1;

	Object.defineProperty(sqrt$1, "__esModule", {
	  value: true
	});
	sqrt$1.createSqrt = void 0;
	var _factory = requireFactory$b();
	const name = 'sqrt';
	const dependencies = ['config', 'typed', 'Complex'];
	sqrt$1.createSqrt = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    config,
	    typed,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the square root of a value.
	   *
	   * For matrices, if you want the matrix square root of a square matrix,
	   * use the `sqrtm` function. If you wish to apply `sqrt` elementwise to
	   * a matrix M, use `math.map(M, math.sqrt)`.
	   *
	   * Syntax:
	   *
	   *    math.sqrt(x)
	   *
	   * Examples:
	   *
	   *    math.sqrt(25)                // returns 5
	   *    math.square(5)               // returns 25
	   *    math.sqrt(-4)                // returns Complex 2i
	   *
	   * See also:
	   *
	   *    square, multiply, cube, cbrt, sqrtm
	   *
	   * @param {number | BigNumber | Complex | Unit} x
	   *            Value for which to calculate the square root.
	   * @return {number | BigNumber | Complex | Unit}
	   *            Returns the square root of `x`
	   */
	  return typed('sqrt', {
	    number: _sqrtNumber,
	    Complex: function (x) {
	      return x.sqrt();
	    },
	    BigNumber: function (x) {
	      if (!x.isNegative() || config.predictable) {
	        return x.sqrt();
	      } else {
	        // negative value -> downgrade to number to do complex value computation
	        return _sqrtNumber(x.toNumber());
	      }
	    },
	    Unit: function (x) {
	      // Someday will work for complex units when they are implemented
	      return x.pow(0.5);
	    }
	  });

	  /**
	   * Calculate sqrt for a number
	   * @param {number} x
	   * @returns {number | Complex} Returns the square root of x
	   * @private
	   */
	  function _sqrtNumber(x) {
	    if (isNaN(x)) {
	      return NaN;
	    } else if (x >= 0 || config.predictable) {
	      return Math.sqrt(x);
	    } else {
	      return new Complex(x, 0).sqrt();
	    }
	  }
	});
	return sqrt$1;
}

var square$1 = {};

var hasRequiredSquare$1;

function requireSquare$1 () {
	if (hasRequiredSquare$1) return square$1;
	hasRequiredSquare$1 = 1;

	Object.defineProperty(square$1, "__esModule", {
	  value: true
	});
	square$1.createSquare = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'square';
	const dependencies = ['typed'];
	square$1.createSquare = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the square of a value, `x * x`.
	   * To avoid confusion with multiplying a square matrix by itself,
	   * this function does not apply to matrices. If you wish to square
	   * every element of a matrix, see the examples.
	   *
	   * Syntax:
	   *
	   *    math.square(x)
	   *
	   * Examples:
	   *
	   *    math.square(2)           // returns number 4
	   *    math.square(3)           // returns number 9
	   *    math.pow(3, 2)           // returns number 9
	   *    math.multiply(3, 3)      // returns number 9
	   *
	   *    math.map([1, 2, 3, 4], math.square)  // returns Array [1, 4, 9, 16]
	   *
	   * See also:
	   *
	   *    multiply, cube, sqrt, pow
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit} x
	   *            Number for which to calculate the square
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit}
	   *            Squared value
	   */
	  return typed(name, {
	    number: _index.squareNumber,
	    Complex: function (x) {
	      return x.mul(x);
	    },
	    BigNumber: function (x) {
	      return x.times(x);
	    },
	    bigint: function (x) {
	      return x * x;
	    },
	    Fraction: function (x) {
	      return x.mul(x);
	    },
	    Unit: function (x) {
	      return x.pow(2);
	    }
	  });
	});
	return square$1;
}

var subtract$2 = {};

var hasRequiredSubtract$1;

function requireSubtract$1 () {
	if (hasRequiredSubtract$1) return subtract$2;
	hasRequiredSubtract$1 = 1;

	Object.defineProperty(subtract$2, "__esModule", {
	  value: true
	});
	subtract$2.createSubtract = void 0;
	var _factory = requireFactory$b();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo05xSfSf = requireMatAlgo05xSfSf();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'subtract';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'subtractScalar', 'unaryMinus', 'DenseMatrix', 'concat'];
	subtract$2.createSubtract = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    subtractScalar,
	    unaryMinus,
	    DenseMatrix,
	    concat
	  } = _ref;
	  // TODO: split function subtract in two: subtract and subtractScalar

	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo05xSfSf = (0, _matAlgo05xSfSf.createMatAlgo05xSfSf)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Subtract two values, `x - y`.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.subtract(x, y)
	   *
	   * Examples:
	   *
	   *    math.subtract(5.3, 2)        // returns number 3.3
	   *
	   *    const a = math.complex(2, 3)
	   *    const b = math.complex(4, 1)
	   *    math.subtract(a, b)          // returns Complex -2 + 2i
	   *
	   *    math.subtract([5, 7, 4], 4)  // returns Array [1, 3, 0]
	   *
	   *    const c = math.unit('2.1 km')
	   *    const d = math.unit('500m')
	   *    math.subtract(c, d)          // returns Unit 1.6 km
	   *
	   * See also:
	   *
	   *    add
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} x Initial value
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} y Value to subtract from `x`
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} Subtraction of `x` and `y`
	   */
	  return typed(name, {
	    'any, any': subtractScalar
	  }, matrixAlgorithmSuite({
	    elop: subtractScalar,
	    SS: matAlgo05xSfSf,
	    DS: matAlgo01xDSid,
	    SD: matAlgo03xDSf,
	    Ss: matAlgo12xSfs,
	    sS: matAlgo10xSids
	  }));
	});
	return subtract$2;
}

var xgcd$1 = {};

var hasRequiredXgcd$1;

function requireXgcd$1 () {
	if (hasRequiredXgcd$1) return xgcd$1;
	hasRequiredXgcd$1 = 1;

	Object.defineProperty(xgcd$1, "__esModule", {
	  value: true
	});
	xgcd$1.createXgcd = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'xgcd';
	const dependencies = ['typed', 'config', 'matrix', 'BigNumber'];
	xgcd$1.createXgcd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the extended greatest common divisor for two values.
	   * See https://en.wikipedia.org/wiki/Extended_Euclidean_algorithm.
	   *
	   * Syntax:
	   *
	   *    math.xgcd(a, b)
	   *
	   * Examples:
	   *
	   *    math.xgcd(8, 12)             // returns [4, -1, 1]
	   *    math.gcd(8, 12)              // returns 4
	   *    math.xgcd(36163, 21199)      // returns [1247, -7, 12]
	   *
	   * See also:
	   *
	   *    gcd, lcm
	   *
	   * @param {number | BigNumber} a  An integer number
	   * @param {number | BigNumber} b  An integer number
	   * @return {Array}              Returns an array containing 3 integers `[div, m, n]`
	   *                              where `div = gcd(a, b)` and `a*m + b*n = div`
	   */
	  return typed(name, {
	    'number, number': function (a, b) {
	      const res = (0, _index.xgcdNumber)(a, b);
	      return config.matrix === 'Array' ? res : matrix(res);
	    },
	    'BigNumber, BigNumber': _xgcdBigNumber
	    // TODO: implement support for Fraction
	  });

	  /**
	   * Calculate xgcd for two BigNumbers
	   * @param {BigNumber} a
	   * @param {BigNumber} b
	   * @return {BigNumber[]} result
	   * @private
	   */
	  function _xgcdBigNumber(a, b) {
	    // source: https://en.wikipedia.org/wiki/Extended_Euclidean_algorithm
	    let
	    // used to swap two variables
	    t;
	    let
	    // quotient
	    q;
	    let
	    // remainder
	    r;
	    const zero = new BigNumber(0);
	    const one = new BigNumber(1);
	    let x = zero;
	    let lastx = one;
	    let y = one;
	    let lasty = zero;
	    if (!a.isInt() || !b.isInt()) {
	      throw new Error('Parameters in function xgcd must be integer numbers');
	    }
	    while (!b.isZero()) {
	      q = a.div(b).floor();
	      r = a.mod(b);
	      t = x;
	      x = lastx.minus(q.times(x));
	      lastx = t;
	      t = y;
	      y = lasty.minus(q.times(y));
	      lasty = t;
	      a = b;
	      b = r;
	    }
	    let res;
	    if (a.lt(zero)) {
	      res = [a.neg(), lastx.neg(), lasty.neg()];
	    } else {
	      res = [a, !a.isZero() ? lastx : 0, lasty];
	    }
	    return config.matrix === 'Array' ? res : matrix(res);
	  }
	});
	return xgcd$1;
}

var invmod$1 = {};

var hasRequiredInvmod$1;

function requireInvmod$1 () {
	if (hasRequiredInvmod$1) return invmod$1;
	hasRequiredInvmod$1 = 1;

	Object.defineProperty(invmod$1, "__esModule", {
	  value: true
	});
	invmod$1.createInvmod = void 0;
	var _factory = requireFactory$b();
	const name = 'invmod';
	const dependencies = ['typed', 'config', 'BigNumber', 'xgcd', 'equal', 'smaller', 'mod', 'add', 'isInteger'];
	invmod$1.createInvmod = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    BigNumber,
	    xgcd,
	    equal,
	    smaller,
	    mod,
	    add,
	    isInteger
	  } = _ref;
	  /**
	   * Calculate the (modular) multiplicative inverse of a modulo b. Solution to the equation `ax ≣ 1 (mod b)`
	   * See https://en.wikipedia.org/wiki/Modular_multiplicative_inverse.
	   *
	   * Syntax:
	   *
	   *    math.invmod(a, b)
	   *
	   * Examples:
	   *
	   *    math.invmod(8, 12)             // returns NaN
	   *    math.invmod(7, 13)             // returns 2
	   *    math.invmod(15151, 15122)      // returns 10429
	   *
	   * See also:
	   *
	   *    gcd, xgcd
	   *
	   * @param {number | BigNumber} a  An integer number
	   * @param {number | BigNumber} b  An integer number
	   * @return {number | BigNumber }  Returns an integer number
	   *                              where `invmod(a,b)*a ≣ 1 (mod b)`
	   */
	  return typed(name, {
	    'number, number': invmod,
	    'BigNumber, BigNumber': invmod
	  });
	  function invmod(a, b) {
	    if (!isInteger(a) || !isInteger(b)) throw new Error('Parameters in function invmod must be integer numbers');
	    a = mod(a, b);
	    if (equal(b, 0)) throw new Error('Divisor must be non zero');
	    let res = xgcd(a, b);
	    res = res.valueOf();
	    let [gcd, inv] = res;
	    if (!equal(gcd, BigNumber(1))) return NaN;
	    inv = mod(inv, b);
	    if (smaller(inv, BigNumber(0))) inv = add(inv, b);
	    return inv;
	  }
	});
	return invmod$1;
}

var dotMultiply$1 = {};

var matAlgo09xS0Sf = {};

var hasRequiredMatAlgo09xS0Sf;

function requireMatAlgo09xS0Sf () {
	if (hasRequiredMatAlgo09xS0Sf) return matAlgo09xS0Sf;
	hasRequiredMatAlgo09xS0Sf = 1;

	Object.defineProperty(matAlgo09xS0Sf, "__esModule", {
	  value: true
	});
	matAlgo09xS0Sf.createMatAlgo09xS0Sf = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo09xS0Sf';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo09xS0Sf.createMatAlgo09xS0Sf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix A and invokes the callback function f(Aij, Bij).
	   * Callback function invoked NZA times, number of nonzero elements in A.
	   *
	   *
	   *          ┌  f(Aij, Bij)  ; A(i,j) !== 0
	   * C(i,j) = ┤
	   *          └  0            ; otherwise
	   *
	   *
	   * @param {Matrix}   a                 The SparseMatrix instance (A)
	   * @param {Matrix}   b                 The SparseMatrix instance (B)
	   * @param {Function} callback          The f(Aij,Bij) operation to invoke
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97620294
	   */
	  return function matAlgo09xS0Sf(a, b, callback) {
	    // sparse matrix arrays
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const asize = a._size;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // sparse matrix arrays
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cvalues = avalues && bvalues ? [] : undefined;
	    const cindex = [];
	    const cptr = [];

	    // workspaces
	    const x = cvalues ? [] : undefined;
	    // marks indicating we have a value in x for a given column
	    const w = [];

	    // vars
	    let i, j, k, k0, k1;

	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // update cptr
	      cptr[j] = cindex.length;
	      // column mark
	      const mark = j + 1;
	      // check we need to process values
	      if (x) {
	        // loop B(:,j)
	        for (k0 = bptr[j], k1 = bptr[j + 1], k = k0; k < k1; k++) {
	          // row
	          i = bindex[k];
	          // update workspace
	          w[i] = mark;
	          x[i] = bvalues[k];
	        }
	      }
	      // loop A(:,j)
	      for (k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        i = aindex[k];
	        // check we need to process values
	        if (x) {
	          // b value @ i,j
	          const vb = w[i] === mark ? x[i] : zero;
	          // invoke f
	          const vc = cf(avalues[k], vb);
	          // check zero value
	          if (!eq(vc, zero)) {
	            // push index
	            cindex.push(i);
	            // push value
	            cvalues.push(vc);
	          }
	        } else {
	          // push index
	          cindex.push(i);
	        }
	      }
	    }
	    // update cptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo09xS0Sf;
}

var hasRequiredDotMultiply$1;

function requireDotMultiply$1 () {
	if (hasRequiredDotMultiply$1) return dotMultiply$1;
	hasRequiredDotMultiply$1 = 1;

	Object.defineProperty(dotMultiply$1, "__esModule", {
	  value: true
	});
	dotMultiply$1.createDotMultiply = void 0;
	var _factory = requireFactory$b();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo09xS0Sf = requireMatAlgo09xS0Sf();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'dotMultiply';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'multiplyScalar', 'concat'];
	dotMultiply$1.createDotMultiply = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    multiplyScalar,
	    concat
	  } = _ref;
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo09xS0Sf = (0, _matAlgo09xS0Sf.createMatAlgo09xS0Sf)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Multiply two matrices element wise. The function accepts both matrices and
	   * scalar values.
	   *
	   * Syntax:
	   *
	   *    math.dotMultiply(x, y)
	   *
	   * Examples:
	   *
	   *    math.dotMultiply(2, 4) // returns 8
	   *
	   *    a = [[9, 5], [6, 1]]
	   *    b = [[3, 2], [5, 2]]
	   *
	   *    math.dotMultiply(a, b) // returns [[27, 10], [30, 2]]
	   *    math.multiply(a, b)    // returns [[52, 28], [23, 14]]
	   *
	   * See also:
	   *
	   *    multiply, divide, dotDivide
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x Left hand value
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} y Right hand value
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix}                    Multiplication of `x` and `y`
	   */
	  return typed(name, matrixAlgorithmSuite({
	    elop: multiplyScalar,
	    SS: matAlgo09xS0Sf,
	    DS: matAlgo02xDS0,
	    Ss: matAlgo11xS0s
	  }));
	});
	return dotMultiply$1;
}

var bitAnd$1 = {};

var bitwise = {};

var hasRequiredBitwise;

function requireBitwise () {
	if (hasRequiredBitwise) return bitwise;
	hasRequiredBitwise = 1;

	Object.defineProperty(bitwise, "__esModule", {
	  value: true
	});
	bitwise.bitAndBigNumber = bitAndBigNumber;
	bitwise.bitNotBigNumber = bitNotBigNumber;
	bitwise.bitOrBigNumber = bitOrBigNumber;
	bitwise.bitXor = bitXor;
	bitwise.bitwise = bitwise$1;
	bitwise.leftShiftBigNumber = leftShiftBigNumber;
	bitwise.rightArithShiftBigNumber = rightArithShiftBigNumber;
	/**
	 * Bitwise and for Bignumbers
	 *
	 * Special Cases:
	 *   N &  n =  N
	 *   n &  0 =  0
	 *   n & -1 =  n
	 *   n &  n =  n
	 *   I &  I =  I
	 *  -I & -I = -I
	 *   I & -I =  0
	 *   I &  n =  n
	 *   I & -n =  I
	 *  -I &  n =  0
	 *  -I & -n = -I
	 *
	 * @param {BigNumber} x
	 * @param {BigNumber} y
	 * @return {BigNumber} Result of `x` & `y`, is fully precise
	 * @private
	 */
	function bitAndBigNumber(x, y) {
	  if (x.isFinite() && !x.isInteger() || y.isFinite() && !y.isInteger()) {
	    throw new Error('Integers expected in function bitAnd');
	  }
	  const BigNumber = x.constructor;
	  if (x.isNaN() || y.isNaN()) {
	    return new BigNumber(NaN);
	  }
	  if (x.isZero() || y.eq(-1) || x.eq(y)) {
	    return x;
	  }
	  if (y.isZero() || x.eq(-1)) {
	    return y;
	  }
	  if (!x.isFinite() || !y.isFinite()) {
	    if (!x.isFinite() && !y.isFinite()) {
	      if (x.isNegative() === y.isNegative()) {
	        return x;
	      }
	      return new BigNumber(0);
	    }
	    if (!x.isFinite()) {
	      if (y.isNegative()) {
	        return x;
	      }
	      if (x.isNegative()) {
	        return new BigNumber(0);
	      }
	      return y;
	    }
	    if (!y.isFinite()) {
	      if (x.isNegative()) {
	        return y;
	      }
	      if (y.isNegative()) {
	        return new BigNumber(0);
	      }
	      return x;
	    }
	  }
	  return bitwise$1(x, y, function (a, b) {
	    return a & b;
	  });
	}

	/**
	 * Bitwise not
	 * @param {BigNumber} x
	 * @return {BigNumber} Result of ~`x`, fully precise
	 *
	 */
	function bitNotBigNumber(x) {
	  if (x.isFinite() && !x.isInteger()) {
	    throw new Error('Integer expected in function bitNot');
	  }
	  const BigNumber = x.constructor;
	  const prevPrec = BigNumber.precision;
	  BigNumber.config({
	    precision: 1E9
	  });
	  const result = x.plus(new BigNumber(1));
	  result.s = -result.s || null;
	  BigNumber.config({
	    precision: prevPrec
	  });
	  return result;
	}

	/**
	 * Bitwise OR for BigNumbers
	 *
	 * Special Cases:
	 *   N |  n =  N
	 *   n |  0 =  n
	 *   n | -1 = -1
	 *   n |  n =  n
	 *   I |  I =  I
	 *  -I | -I = -I
	 *   I | -n = -1
	 *   I | -I = -1
	 *   I |  n =  I
	 *  -I |  n = -I
	 *  -I | -n = -n
	 *
	 * @param {BigNumber} x
	 * @param {BigNumber} y
	 * @return {BigNumber} Result of `x` | `y`, fully precise
	 */
	function bitOrBigNumber(x, y) {
	  if (x.isFinite() && !x.isInteger() || y.isFinite() && !y.isInteger()) {
	    throw new Error('Integers expected in function bitOr');
	  }
	  const BigNumber = x.constructor;
	  if (x.isNaN() || y.isNaN()) {
	    return new BigNumber(NaN);
	  }
	  const negOne = new BigNumber(-1);
	  if (x.isZero() || y.eq(negOne) || x.eq(y)) {
	    return y;
	  }
	  if (y.isZero() || x.eq(negOne)) {
	    return x;
	  }
	  if (!x.isFinite() || !y.isFinite()) {
	    if (!x.isFinite() && !x.isNegative() && y.isNegative() || x.isNegative() && !y.isNegative() && !y.isFinite()) {
	      return negOne;
	    }
	    if (x.isNegative() && y.isNegative()) {
	      return x.isFinite() ? x : y;
	    }
	    return x.isFinite() ? y : x;
	  }
	  return bitwise$1(x, y, function (a, b) {
	    return a | b;
	  });
	}

	/**
	 * Applies bitwise function to numbers
	 * @param {BigNumber} x
	 * @param {BigNumber} y
	 * @param {function (a, b)} func
	 * @return {BigNumber}
	 */
	function bitwise$1(x, y, func) {
	  const BigNumber = x.constructor;
	  let xBits, yBits;
	  const xSign = +(x.s < 0);
	  const ySign = +(y.s < 0);
	  if (xSign) {
	    xBits = decCoefficientToBinaryString(bitNotBigNumber(x));
	    for (let i = 0; i < xBits.length; ++i) {
	      xBits[i] ^= 1;
	    }
	  } else {
	    xBits = decCoefficientToBinaryString(x);
	  }
	  if (ySign) {
	    yBits = decCoefficientToBinaryString(bitNotBigNumber(y));
	    for (let i = 0; i < yBits.length; ++i) {
	      yBits[i] ^= 1;
	    }
	  } else {
	    yBits = decCoefficientToBinaryString(y);
	  }
	  let minBits, maxBits, minSign;
	  if (xBits.length <= yBits.length) {
	    minBits = xBits;
	    maxBits = yBits;
	    minSign = xSign;
	  } else {
	    minBits = yBits;
	    maxBits = xBits;
	    minSign = ySign;
	  }
	  let shortLen = minBits.length;
	  let longLen = maxBits.length;
	  const expFuncVal = func(xSign, ySign) ^ 1;
	  let outVal = new BigNumber(expFuncVal ^ 1);
	  let twoPower = new BigNumber(1);
	  const two = new BigNumber(2);
	  const prevPrec = BigNumber.precision;
	  BigNumber.config({
	    precision: 1E9
	  });
	  while (shortLen > 0) {
	    if (func(minBits[--shortLen], maxBits[--longLen]) === expFuncVal) {
	      outVal = outVal.plus(twoPower);
	    }
	    twoPower = twoPower.times(two);
	  }
	  while (longLen > 0) {
	    if (func(minSign, maxBits[--longLen]) === expFuncVal) {
	      outVal = outVal.plus(twoPower);
	    }
	    twoPower = twoPower.times(two);
	  }
	  BigNumber.config({
	    precision: prevPrec
	  });
	  if (expFuncVal === 0) {
	    outVal.s = -outVal.s;
	  }
	  return outVal;
	}

	/* Extracted from decimal.js, and edited to specialize. */
	function decCoefficientToBinaryString(x) {
	  // Convert to string
	  const a = x.d; // array with digits
	  let r = a[0] + '';
	  for (let i = 1; i < a.length; ++i) {
	    let s = a[i] + '';
	    for (let z = 7 - s.length; z--;) {
	      s = '0' + s;
	    }
	    r += s;
	  }
	  let j = r.length;
	  while (r.charAt(j) === '0') {
	    j--;
	  }
	  let xe = x.e;
	  let str = r.slice(0, j + 1 || 1);
	  const strL = str.length;
	  if (xe > 0) {
	    if (++xe > strL) {
	      // Append zeros.
	      xe -= strL;
	      while (xe--) {
	        str += '0';
	      }
	    } else if (xe < strL) {
	      str = str.slice(0, xe) + '.' + str.slice(xe);
	    }
	  }

	  // Convert from base 10 (decimal) to base 2
	  const arr = [0];
	  for (let i = 0; i < str.length;) {
	    let arrL = arr.length;
	    while (arrL--) {
	      arr[arrL] *= 10;
	    }
	    arr[0] += parseInt(str.charAt(i++)); // convert to int
	    for (let j = 0; j < arr.length; ++j) {
	      if (arr[j] > 1) {
	        if (arr[j + 1] === null || arr[j + 1] === undefined) {
	          arr[j + 1] = 0;
	        }
	        arr[j + 1] += arr[j] >> 1;
	        arr[j] &= 1;
	      }
	    }
	  }
	  return arr.reverse();
	}

	/**
	 * Bitwise XOR for BigNumbers
	 *
	 * Special Cases:
	 *   N ^  n =  N
	 *   n ^  0 =  n
	 *   n ^  n =  0
	 *   n ^ -1 = ~n
	 *   I ^  n =  I
	 *   I ^ -n = -I
	 *   I ^ -I = -1
	 *  -I ^  n = -I
	 *  -I ^ -n =  I
	 *
	 * @param {BigNumber} x
	 * @param {BigNumber} y
	 * @return {BigNumber} Result of `x` ^ `y`, fully precise
	 *
	 */
	function bitXor(x, y) {
	  if (x.isFinite() && !x.isInteger() || y.isFinite() && !y.isInteger()) {
	    throw new Error('Integers expected in function bitXor');
	  }
	  const BigNumber = x.constructor;
	  if (x.isNaN() || y.isNaN()) {
	    return new BigNumber(NaN);
	  }
	  if (x.isZero()) {
	    return y;
	  }
	  if (y.isZero()) {
	    return x;
	  }
	  if (x.eq(y)) {
	    return new BigNumber(0);
	  }
	  const negOne = new BigNumber(-1);
	  if (x.eq(negOne)) {
	    return bitNotBigNumber(y);
	  }
	  if (y.eq(negOne)) {
	    return bitNotBigNumber(x);
	  }
	  if (!x.isFinite() || !y.isFinite()) {
	    if (!x.isFinite() && !y.isFinite()) {
	      return negOne;
	    }
	    return new BigNumber(x.isNegative() === y.isNegative() ? Infinity : -Infinity);
	  }
	  return bitwise$1(x, y, function (a, b) {
	    return a ^ b;
	  });
	}

	/**
	 * Bitwise left shift
	 *
	 * Special Cases:
	 *  n << -n = N
	 *  n <<  N = N
	 *  N <<  n = N
	 *  n <<  0 = n
	 *  0 <<  n = 0
	 *  I <<  I = N
	 *  I <<  n = I
	 *  n <<  I = I
	 *
	 * @param {BigNumber} x
	 * @param {BigNumber} y
	 * @return {BigNumber} Result of `x` << `y`
	 *
	 */
	function leftShiftBigNumber(x, y) {
	  if (x.isFinite() && !x.isInteger() || y.isFinite() && !y.isInteger()) {
	    throw new Error('Integers expected in function leftShift');
	  }
	  const BigNumber = x.constructor;
	  if (x.isNaN() || y.isNaN() || y.isNegative() && !y.isZero()) {
	    return new BigNumber(NaN);
	  }
	  if (x.isZero() || y.isZero()) {
	    return x;
	  }
	  if (!x.isFinite() && !y.isFinite()) {
	    return new BigNumber(NaN);
	  }

	  // Math.pow(2, y) is fully precise for y < 55, and fast
	  if (y.lt(55)) {
	    return x.times(Math.pow(2, y.toNumber()) + '');
	  }
	  return x.times(new BigNumber(2).pow(y));
	}

	/*
	 * Special Cases:
	 *   n >> -n =  N
	 *   n >>  N =  N
	 *   N >>  n =  N
	 *   I >>  I =  N
	 *   n >>  0 =  n
	 *   I >>  n =  I
	 *  -I >>  n = -I
	 *  -I >>  I = -I
	 *   n >>  I =  I
	 *  -n >>  I = -1
	 *   0 >>  n =  0
	 *
	 * @param {BigNumber} value
	 * @param {BigNumber} value
	 * @return {BigNumber} Result of `x` >> `y`
	 *
	 */
	function rightArithShiftBigNumber(x, y) {
	  if (x.isFinite() && !x.isInteger() || y.isFinite() && !y.isInteger()) {
	    throw new Error('Integers expected in function rightArithShift');
	  }
	  const BigNumber = x.constructor;
	  if (x.isNaN() || y.isNaN() || y.isNegative() && !y.isZero()) {
	    return new BigNumber(NaN);
	  }
	  if (x.isZero() || y.isZero()) {
	    return x;
	  }
	  if (!y.isFinite()) {
	    if (x.isNegative()) {
	      return new BigNumber(-1);
	    }
	    if (!x.isFinite()) {
	      return new BigNumber(NaN);
	    }
	    return new BigNumber(0);
	  }

	  // Math.pow(2, y) is fully precise for y < 55, and fast
	  if (y.lt(55)) {
	    return x.div(Math.pow(2, y.toNumber()) + '').floor();
	  }
	  return x.div(new BigNumber(2).pow(y)).floor();
	}
	return bitwise;
}

var hasRequiredBitAnd$1;

function requireBitAnd$1 () {
	if (hasRequiredBitAnd$1) return bitAnd$1;
	hasRequiredBitAnd$1 = 1;

	Object.defineProperty(bitAnd$1, "__esModule", {
	  value: true
	});
	bitAnd$1.createBitAnd = void 0;
	var _bitwise = requireBitwise();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo06xS0S = requireMatAlgo06xS0S0();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'bitAnd';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'concat'];
	bitAnd$1.createBitAnd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    concat
	  } = _ref;
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo06xS0S0 = (0, _matAlgo06xS0S.createMatAlgo06xS0S0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Bitwise AND two values, `x & y`.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.bitAnd(x, y)
	   *
	   * Examples:
	   *
	   *    math.bitAnd(53, 131)               // returns number 1
	   *
	   *    math.bitAnd([1, 12, 31], 42)       // returns Array [0, 8, 10]
	   *
	   * See also:
	   *
	   *    bitNot, bitOr, bitXor, leftShift, rightArithShift, rightLogShift
	   *
	   * @param  {number | BigNumber | bigint | Array | Matrix} x First value to and
	   * @param  {number | BigNumber | bigint | Array | Matrix} y Second value to and
	   * @return {number | BigNumber | bigint | Array | Matrix} AND of `x` and `y`
	   */
	  return typed(name, {
	    'number, number': _index.bitAndNumber,
	    'BigNumber, BigNumber': _bitwise.bitAndBigNumber,
	    'bigint, bigint': (x, y) => x & y
	  }, matrixAlgorithmSuite({
	    SS: matAlgo06xS0S0,
	    DS: matAlgo02xDS0,
	    Ss: matAlgo11xS0s
	  }));
	});
	return bitAnd$1;
}

var bitNot$1 = {};

var hasRequiredBitNot$1;

function requireBitNot$1 () {
	if (hasRequiredBitNot$1) return bitNot$1;
	hasRequiredBitNot$1 = 1;

	Object.defineProperty(bitNot$1, "__esModule", {
	  value: true
	});
	bitNot$1.createBitNot = void 0;
	var _bitwise = requireBitwise();
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'bitNot';
	const dependencies = ['typed'];
	bitNot$1.createBitNot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Bitwise NOT value, `~x`.
	   * For matrices, the function is evaluated element wise.
	   * For units, the function is evaluated on the best prefix base.
	   *
	   * Syntax:
	   *
	   *    math.bitNot(x)
	   *
	   * Examples:
	   *
	   *    math.bitNot(1)               // returns number -2
	   *
	   *    math.bitNot([2, -3, 4])      // returns Array [-3, 2, -5]
	   *
	   * See also:
	   *
	   *    bitAnd, bitOr, bitXor, leftShift, rightArithShift, rightLogShift
	   *
	   * @param  {number | BigNumber | bigint | Array | Matrix} x Value to not
	   * @return {number | BigNumber | bigint | Array | Matrix} NOT of `x`
	   */
	  return typed(name, {
	    number: _index.bitNotNumber,
	    BigNumber: _bitwise.bitNotBigNumber,
	    bigint: x => ~x,
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return bitNot$1;
}

var bitOr$1 = {};

var hasRequiredBitOr$1;

function requireBitOr$1 () {
	if (hasRequiredBitOr$1) return bitOr$1;
	hasRequiredBitOr$1 = 1;

	Object.defineProperty(bitOr$1, "__esModule", {
	  value: true
	});
	bitOr$1.createBitOr = void 0;
	var _bitwise = requireBitwise();
	var _factory = requireFactory$b();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matAlgo04xSidSid = requireMatAlgo04xSidSid();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'bitOr';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'DenseMatrix', 'concat'];
	bitOr$1.createBitOr = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo04xSidSid = (0, _matAlgo04xSidSid.createMatAlgo04xSidSid)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Bitwise OR two values, `x | y`.
	   * For matrices, the function is evaluated element wise.
	   * For units, the function is evaluated on the lowest print base.
	   *
	   * Syntax:
	   *
	   *    math.bitOr(x, y)
	   *
	   * Examples:
	   *
	   *    math.bitOr(1, 2)               // returns number 3
	   *
	   *    math.bitOr([1, 2, 3], 4)       // returns Array [5, 6, 7]
	   *
	   * See also:
	   *
	   *    bitAnd, bitNot, bitXor, leftShift, rightArithShift, rightLogShift
	   *
	   * @param  {number | BigNumber | bigint | Array | Matrix} x First value to or
	   * @param  {number | BigNumber | bigint | Array | Matrix} y Second value to or
	   * @return {number | BigNumber | bigint | Array | Matrix} OR of `x` and `y`
	   */
	  return typed(name, {
	    'number, number': _index.bitOrNumber,
	    'BigNumber, BigNumber': _bitwise.bitOrBigNumber,
	    'bigint, bigint': (x, y) => x | y
	  }, matrixAlgorithmSuite({
	    SS: matAlgo04xSidSid,
	    DS: matAlgo01xDSid,
	    Ss: matAlgo10xSids
	  }));
	});
	return bitOr$1;
}

var bitXor$1 = {};

var matAlgo07xSSf = {};

var hasRequiredMatAlgo07xSSf;

function requireMatAlgo07xSSf () {
	if (hasRequiredMatAlgo07xSSf) return matAlgo07xSSf;
	hasRequiredMatAlgo07xSSf = 1;

	Object.defineProperty(matAlgo07xSSf, "__esModule", {
	  value: true
	});
	matAlgo07xSSf.createMatAlgo07xSSf = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo07xSSf';
	const dependencies = ['typed', 'SparseMatrix'];
	matAlgo07xSSf.createMatAlgo07xSSf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    SparseMatrix
	  } = _ref;
	  /**
	  * Iterates over SparseMatrix A and SparseMatrix B items (zero and nonzero) and invokes the callback function f(Aij, Bij).
	  * Callback function invoked MxN times.
	  *
	  * C(i,j) = f(Aij, Bij)
	  *
	  * @param {Matrix}   a                 The SparseMatrix instance (A)
	  * @param {Matrix}   b                 The SparseMatrix instance (B)
	  * @param {Function} callback          The f(Aij,Bij) operation to invoke
	  *
	  * @return {Matrix}                    SparseMatrix (C)
	  *
	  * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97620294
	  */
	  return function matAlgo07xSSf(a, b, callback) {
	    // sparse matrix arrays
	    const asize = a._size;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    let zero = 0;
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      dt = adt;
	      zero = typed.convert(0, dt);
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays for sparse format
	    const cvalues = [];
	    const cindex = [];
	    const cptr = new Array(columns + 1).fill(0); // Start with column pointer array

	    // workspaces
	    const xa = [];
	    const xb = [];
	    const wa = [];
	    const wb = [];

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      const mark = j + 1;
	      let nonZeroCount = 0;
	      _scatter(a, j, wa, xa, mark);
	      _scatter(b, j, wb, xb, mark);

	      // loop rows
	      for (let i = 0; i < rows; i++) {
	        const va = wa[i] === mark ? xa[i] : zero;
	        const vb = wb[i] === mark ? xb[i] : zero;

	        // invoke callback
	        const cij = cf(va, vb);
	        // Store all non zero and true values
	        if (cij !== 0 && cij !== false) {
	          cindex.push(i); // row index
	          cvalues.push(cij); // computed value
	          nonZeroCount++;
	        }
	      }

	      // Update column pointer with cumulative count of non-zero values
	      cptr[j + 1] = cptr[j] + nonZeroCount;
	    }

	    // Return the result as a sparse matrix
	    return new SparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  };
	  function _scatter(m, j, w, x, mark) {
	    // a arrays
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;
	    // loop values in column j
	    for (let k = ptr[j], k1 = ptr[j + 1]; k < k1; k++) {
	      // row
	      const i = index[k];
	      // update workspace
	      w[i] = mark;
	      x[i] = values[k];
	    }
	  }
	});
	return matAlgo07xSSf;
}

var hasRequiredBitXor$1;

function requireBitXor$1 () {
	if (hasRequiredBitXor$1) return bitXor$1;
	hasRequiredBitXor$1 = 1;

	Object.defineProperty(bitXor$1, "__esModule", {
	  value: true
	});
	bitXor$1.createBitXor = void 0;
	var _bitwise = requireBitwise();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'bitXor';
	const dependencies = ['typed', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	bitXor$1.createBitXor = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Bitwise XOR two values, `x ^ y`.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.bitXor(x, y)
	   *
	   * Examples:
	   *
	   *    math.bitXor(1, 2)               // returns number 3
	   *
	   *    math.bitXor([2, 3, 4], 4)       // returns Array [6, 7, 0]
	   *
	   * See also:
	   *
	   *    bitAnd, bitNot, bitOr, leftShift, rightArithShift, rightLogShift
	   *
	   * @param  {number | BigNumber | bigint | Array | Matrix} x First value to xor
	   * @param  {number | BigNumber | bigint | Array | Matrix} y Second value to xor
	   * @return {number | BigNumber | bigint | Array | Matrix} XOR of `x` and `y`
	   */
	  return typed(name, {
	    'number, number': _index.bitXorNumber,
	    'BigNumber, BigNumber': _bitwise.bitXor,
	    'bigint, bigint': (x, y) => x ^ y
	  }, matrixAlgorithmSuite({
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	return bitXor$1;
}

var arg$1 = {};

var hasRequiredArg$1;

function requireArg$1 () {
	if (hasRequiredArg$1) return arg$1;
	hasRequiredArg$1 = 1;

	Object.defineProperty(arg$1, "__esModule", {
	  value: true
	});
	arg$1.createArg = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'arg';
	const dependencies = ['typed'];
	arg$1.createArg = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the argument of a complex value.
	   * For a complex number `a + bi`, the argument is computed as `atan2(b, a)`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.arg(x)
	   *
	   * Examples:
	   *
	   *    const a = math.complex(2, 2)
	   *    math.arg(a) / math.pi          // returns number 0.25
	   *
	   *    const b = math.complex('2 + 3i')
	   *    math.arg(b)                    // returns number 0.982793723247329
	   *    math.atan2(3, 2)               // returns number 0.982793723247329
	   *
	   * See also:
	   *
	   *    re, im, conj, abs
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix} x
	   *            A complex number or array with complex numbers
	   * @return {number | BigNumber | Array | Matrix} The argument of x
	   */
	  return typed(name, {
	    number: function (x) {
	      return Math.atan2(0, x);
	    },
	    BigNumber: function (x) {
	      return x.constructor.atan2(0, x);
	    },
	    Complex: function (x) {
	      return x.arg();
	    },
	    // TODO: implement BigNumber support for function arg

	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return arg$1;
}

var conj$1 = {};

var hasRequiredConj$1;

function requireConj$1 () {
	if (hasRequiredConj$1) return conj$1;
	hasRequiredConj$1 = 1;

	Object.defineProperty(conj$1, "__esModule", {
	  value: true
	});
	conj$1.createConj = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'conj';
	const dependencies = ['typed'];
	conj$1.createConj = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the complex conjugate of a complex value.
	   * If `x = a+bi`, the complex conjugate of `x` is `a - bi`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.conj(x)
	   *
	   * Examples:
	   *
	   *    math.conj(math.complex('2 + 3i'))  // returns Complex 2 - 3i
	   *    math.conj(math.complex('2 - 3i'))  // returns Complex 2 + 3i
	   *    math.conj(math.complex('-5.2i'))  // returns Complex 5.2i
	   *
	   * See also:
	   *
	   *    re, im, arg, abs
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix | Unit} x
	   *            A complex number or array with complex numbers
	   * @return {number | BigNumber | Complex | Array | Matrix | Unit}
	   *            The complex conjugate of x
	   */
	  return typed(name, {
	    'number | BigNumber | Fraction': x => x,
	    Complex: x => x.conjugate(),
	    Unit: typed.referToSelf(self => x => new x.constructor(self(x.toNumeric()), x.formatUnits())),
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return conj$1;
}

var im$1 = {};

var hasRequiredIm$1;

function requireIm$1 () {
	if (hasRequiredIm$1) return im$1;
	hasRequiredIm$1 = 1;

	Object.defineProperty(im$1, "__esModule", {
	  value: true
	});
	im$1.createIm = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'im';
	const dependencies = ['typed'];
	im$1.createIm = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Get the imaginary part of a complex number.
	   * For a complex number `a + bi`, the function returns `b`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.im(x)
	   *
	   * Examples:
	   *
	   *    const a = math.complex(2, 3)
	   *    math.re(a)                     // returns number 2
	   *    math.im(a)                     // returns number 3
	   *
	   *    math.re(math.complex('-5.2i')) // returns number -5.2
	   *    math.re(math.complex(2.4))     // returns number 0
	   *
	   * See also:
	   *
	   *    re, conj, abs, arg
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix} x
	   *            A complex number or array with complex numbers
	   * @return {number | BigNumber | Array | Matrix} The imaginary part of x
	   */
	  return typed(name, {
	    number: () => 0,
	    'BigNumber | Fraction': x => x.mul(0),
	    Complex: x => x.im,
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return im$1;
}

var re$1 = {};

var hasRequiredRe$1;

function requireRe$1 () {
	if (hasRequiredRe$1) return re$1;
	hasRequiredRe$1 = 1;

	Object.defineProperty(re$1, "__esModule", {
	  value: true
	});
	re$1.createRe = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 're';
	const dependencies = ['typed'];
	re$1.createRe = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Get the real part of a complex number.
	   * For a complex number `a + bi`, the function returns `a`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.re(x)
	   *
	   * Examples:
	   *
	   *    const a = math.complex(2, 3)
	   *    math.re(a)                     // returns number 2
	   *    math.im(a)                     // returns number 3
	   *
	   *    math.re(math.complex('-5.2i')) // returns number 0
	   *    math.re(math.complex(2.4))     // returns number 2.4
	   *
	   * See also:
	   *
	   *    im, conj, abs, arg
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix} x
	   *            A complex number or array with complex numbers
	   * @return {number | BigNumber | Array | Matrix} The real part of x
	   */
	  return typed(name, {
	    'number | BigNumber | Fraction': x => x,
	    Complex: x => x.re,
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return re$1;
}

var not$1 = {};

var hasRequiredNot$1;

function requireNot$1 () {
	if (hasRequiredNot$1) return not$1;
	hasRequiredNot$1 = 1;

	Object.defineProperty(not$1, "__esModule", {
	  value: true
	});
	not$1.createNot = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'not';
	const dependencies = ['typed'];
	not$1.createNot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Logical `not`. Flips boolean value of a given parameter.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.not(x)
	   *
	   * Examples:
	   *
	   *    math.not(2)      // returns false
	   *    math.not(0)      // returns true
	   *    math.not(true)   // returns false
	   *
	   *    a = [2, -7, 0]
	   *    math.not(a)      // returns [false, false, true]
	   *
	   * See also:
	   *
	   *    and, or, xor
	   *
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} x First value to check
	   * @return {boolean | Array | Matrix}
	   *            Returns true when input is a zero or empty value.
	   */
	  return typed(name, {
	    'null | undefined': () => true,
	    number: _index.notNumber,
	    Complex: function (x) {
	      return x.re === 0 && x.im === 0;
	    },
	    BigNumber: function (x) {
	      return x.isZero() || x.isNaN();
	    },
	    bigint: x => !x,
	    Unit: typed.referToSelf(self => x => typed.find(self, x.valueType())(x.value)),
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return not$1;
}

var nullish$1 = {};

var hasRequiredNullish$1;

function requireNullish$1 () {
	if (hasRequiredNullish$1) return nullish$1;
	hasRequiredNullish$1 = 1;

	Object.defineProperty(nullish$1, "__esModule", {
	  value: true
	});
	nullish$1.createNullish = void 0;
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _matAlgo13xDD = requireMatAlgo13xDD();
	var _DimensionError = requireDimensionError();
	const name = 'nullish';
	const dependencies = ['typed', 'matrix', 'size', 'flatten', 'deepEqual'];
	nullish$1.createNullish = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    size,
	    flatten,
	    deepEqual
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const matAlgo13xDD = (0, _matAlgo13xDD.createMatAlgo13xDD)({
	    typed
	  });

	  /**
	   * Nullish coalescing operator (??). Returns the right-hand side operand
	   * when the left-hand side operand is null or undefined, and otherwise
	   * returns the left-hand side operand.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.nullish(x, y)
	   *
	   * Examples:
	   *
	   *    math.nullish(null, 42)        // returns 42
	   *    math.nullish(undefined, 42)   // returns 42
	   *    math.nullish(0, 42)           // returns 0
	   *    math.nullish(false, 42)       // returns false
	   *    math.nullish('', 42)          // returns ''
	   *
	   *    // Object property access with fallback
	   *    const obj = {foo: 7, bar: 3}
	   *    math.nullish(obj.baz, 0)      // returns 0
	   *
	   * See also:
	   *
	   *    and, or, not
	   *
	   * @param  {*} x First value to check
	   * @param  {*} y Fallback value
	   * @return {*} Returns y when x is null or undefined, otherwise returns x
	   */

	  return typed(name, {
	    // Scalar and SparseMatrix-first short-circuit handlers
	    'number|bigint|Complex|BigNumber|Fraction|Unit|string|boolean|SparseMatrix, any': (x, _y) => x,
	    'null, any': (_x, y) => y,
	    'undefined, any': (_x, y) => y,
	    // SparseMatrix-first with collection RHS: enforce exact shape match
	    'SparseMatrix, Array | Matrix': (x, y) => {
	      const sx = size(x);
	      const sy = size(y);
	      if (deepEqual(sx, sy)) return x;
	      throw new _DimensionError.DimensionError(sx, sy);
	    },
	    // DenseMatrix-first handlers (no broadcasting between collections)
	    'DenseMatrix, DenseMatrix': typed.referToSelf(self => (x, y) => matAlgo13xDD(x, y, self)),
	    'DenseMatrix, SparseMatrix': typed.referToSelf(self => (x, y) => matAlgo03xDSf(x, y, self, false)),
	    'DenseMatrix, Array': typed.referToSelf(self => (x, y) => matAlgo13xDD(x, matrix(y), self)),
	    'DenseMatrix, any': typed.referToSelf(self => (x, y) => matAlgo14xDs(x, y, self, false)),
	    // Array-first handlers (bridge via matrix() where needed)
	    'Array, Array': typed.referToSelf(self => (x, y) => matAlgo13xDD(matrix(x), matrix(y), self).valueOf()),
	    'Array, DenseMatrix': typed.referToSelf(self => (x, y) => matAlgo13xDD(matrix(x), y, self)),
	    'Array, SparseMatrix': typed.referToSelf(self => (x, y) => matAlgo03xDSf(matrix(x), y, self, false)),
	    'Array, any': typed.referToSelf(self => (x, y) => matAlgo14xDs(matrix(x), y, self, false).valueOf())
	  });
	});
	return nullish$1;
}

var or$1 = {};

var hasRequiredOr$1;

function requireOr$1 () {
	if (hasRequiredOr$1) return or$1;
	hasRequiredOr$1 = 1;

	Object.defineProperty(or$1, "__esModule", {
	  value: true
	});
	or$1.createOr = void 0;
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matAlgo05xSfSf = requireMatAlgo05xSfSf();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'or';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'DenseMatrix', 'concat'];
	or$1.createOr = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo05xSfSf = (0, _matAlgo05xSfSf.createMatAlgo05xSfSf)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Logical `or`. Test if at least one value is defined with a nonzero/nonempty value.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.or(x, y)
	   *
	   * Examples:
	   *
	   *    math.or(2, 4)   // returns true
	   *
	   *    a = [2, 5, 0]
	   *    b = [0, 22, 0]
	   *    c = 0
	   *
	   *    math.or(a, b)   // returns [true, true, false]
	   *    math.or(b, c)   // returns [false, true, false]
	   *
	   * See also:
	   *
	   *    and, not, xor
	   *
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} x First value to check
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} y Second value to check
	   * @return {boolean | Array | Matrix}
	   *            Returns true when one of the inputs is defined with a nonzero/nonempty value.
	   */
	  return typed(name, {
	    'number, number': _index.orNumber,
	    'Complex, Complex': function (x, y) {
	      return x.re !== 0 || x.im !== 0 || y.re !== 0 || y.im !== 0;
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return !x.isZero() && !x.isNaN() || !y.isZero() && !y.isNaN();
	    },
	    'bigint, bigint': _index.orNumber,
	    'Unit, Unit': typed.referToSelf(self => (x, y) => self(x.value || 0, y.value || 0))
	  }, matrixAlgorithmSuite({
	    SS: matAlgo05xSfSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	return or$1;
}

var xor$1 = {};

var hasRequiredXor$1;

function requireXor$1 () {
	if (hasRequiredXor$1) return xor$1;
	hasRequiredXor$1 = 1;

	Object.defineProperty(xor$1, "__esModule", {
	  value: true
	});
	xor$1.createXor = void 0;
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'xor';
	const dependencies = ['typed', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	xor$1.createXor = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Logical `xor`. Test whether one and only one value is defined with a nonzero/nonempty value.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.xor(x, y)
	   *
	   * Examples:
	   *
	   *    math.xor(2, 4)   // returns false
	   *
	   *    a = [2, 0, 0]
	   *    b = [2, 7, 0]
	   *    c = 0
	   *
	   *    math.xor(a, b)   // returns [false, true, false]
	   *    math.xor(a, c)   // returns [true, false, false]
	   *
	   * See also:
	   *
	   *    and, not, or
	   *
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} x First value to check
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} y Second value to check
	   * @return {boolean | Array | Matrix}
	   *            Returns true when one and only one input is defined with a nonzero/nonempty value.
	   */
	  return typed(name, {
	    'number, number': _index.xorNumber,
	    'Complex, Complex': function (x, y) {
	      return (x.re !== 0 || x.im !== 0) !== (y.re !== 0 || y.im !== 0);
	    },
	    'bigint, bigint': _index.xorNumber,
	    'BigNumber, BigNumber': function (x, y) {
	      return (!x.isZero() && !x.isNaN()) !== (!y.isZero() && !y.isNaN());
	    },
	    'Unit, Unit': typed.referToSelf(self => (x, y) => self(x.value || 0, y.value || 0))
	  }, matrixAlgorithmSuite({
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	return xor$1;
}

var concat$1 = {};

var hasRequiredConcat$1;

function requireConcat$1 () {
	if (hasRequiredConcat$1) return concat$1;
	hasRequiredConcat$1 = 1;

	Object.defineProperty(concat$1, "__esModule", {
	  value: true
	});
	concat$1.createConcat = void 0;
	var _is = requireIs();
	var _object = requireObject$3();
	var _array = requireArray();
	var _IndexError = requireIndexError();
	var _DimensionError = requireDimensionError();
	var _factory = requireFactory$b();
	const name = 'concat';
	const dependencies = ['typed', 'matrix', 'isInteger'];
	concat$1.createConcat = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    isInteger
	  } = _ref;
	  /**
	   * Concatenate two or more matrices.
	   *
	   * Syntax:
	   *
	   *     math.concat(A, B, C, ...)
	   *     math.concat(A, B, C, ..., dim)
	   *
	   * Where:
	   *
	   * - `dim: number` is a zero-based dimension over which to concatenate the matrices.
	   *   By default the last dimension of the matrices.
	   *
	   * Examples:
	   *
	   *    const A = [[1, 2], [5, 6]]
	   *    const B = [[3, 4], [7, 8]]
	   *
	   *    math.concat(A, B)                  // returns [[1, 2, 3, 4], [5, 6, 7, 8]]
	   *    math.concat(A, B, 0)               // returns [[1, 2], [5, 6], [3, 4], [7, 8]]
	   *    math.concat('hello', ' ', 'world') // returns 'hello world'
	   *
	   * See also:
	   *
	   *    size, squeeze, subset, transpose
	   *
	   * @param {... Array | Matrix} args     Two or more matrices
	   * @return {Array | Matrix} Concatenated matrix
	   */
	  return typed(name, {
	    // TODO: change signature to '...Array | Matrix, dim?' when supported
	    '...Array | Matrix | number | BigNumber': function (args) {
	      let i;
	      const len = args.length;
	      let dim = -1; // zero-based dimension
	      let prevDim;
	      let asMatrix = false;
	      const matrices = []; // contains multi dimensional arrays

	      for (i = 0; i < len; i++) {
	        const arg = args[i];

	        // test whether we need to return a Matrix (if not we return an Array)
	        if ((0, _is.isMatrix)(arg)) {
	          asMatrix = true;
	        }
	        if ((0, _is.isNumber)(arg) || (0, _is.isBigNumber)(arg)) {
	          if (i !== len - 1) {
	            throw new Error('Dimension must be specified as last argument');
	          }

	          // last argument contains the dimension on which to concatenate
	          prevDim = dim;
	          dim = arg.valueOf(); // change BigNumber to number

	          if (!isInteger(dim)) {
	            throw new TypeError('Integer number expected for dimension');
	          }
	          if (dim < 0 || i > 0 && dim > prevDim) {
	            // TODO: would be more clear when throwing a DimensionError here
	            throw new _IndexError.IndexError(dim, prevDim + 1);
	          }
	        } else {
	          // this is a matrix or array
	          const m = (0, _object.clone)(arg).valueOf();
	          const size = (0, _array.arraySize)(m);
	          matrices[i] = m;
	          prevDim = dim;
	          dim = size.length - 1;

	          // verify whether each of the matrices has the same number of dimensions
	          if (i > 0 && dim !== prevDim) {
	            throw new _DimensionError.DimensionError(prevDim + 1, dim + 1);
	          }
	        }
	      }
	      if (matrices.length === 0) {
	        throw new SyntaxError('At least one matrix expected');
	      }
	      let res = matrices.shift();
	      while (matrices.length) {
	        res = (0, _array.concat)(res, matrices.shift(), dim);
	      }
	      return asMatrix ? matrix(res) : res;
	    },
	    '...string': function (args) {
	      return args.join('');
	    }
	  });
	});
	return concat$1;
}

var column$1 = {};

var hasRequiredColumn$1;

function requireColumn$1 () {
	if (hasRequiredColumn$1) return column$1;
	hasRequiredColumn$1 = 1;

	Object.defineProperty(column$1, "__esModule", {
	  value: true
	});
	column$1.createColumn = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _object = requireObject$3();
	var _array = requireArray();
	const name = 'column';
	const dependencies = ['typed', 'Index', 'matrix', 'range'];
	column$1.createColumn = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Index,
	    matrix,
	    range
	  } = _ref;
	  /**
	   * Return a column from a Matrix.
	   *
	   * Syntax:
	   *
	   *     math.column(value, index)
	   *
	   * Example:
	   *
	   *     // get a column
	   *     const d = [[1, 2], [3, 4]]
	   *     math.column(d, 1) // returns [[2], [4]]
	   *
	   * See also:
	   *
	   *     row
	   *
	   * @param {Array | Matrix } value   An array or matrix
	   * @param {number} column           The index of the column
	   * @return {Array | Matrix}         The retrieved column
	   */
	  return typed(name, {
	    'Matrix, number': _column,
	    'Array, number': function (value, column) {
	      return _column(matrix((0, _object.clone)(value)), column).valueOf();
	    }
	  });

	  /**
	   * Retrieve a column of a matrix
	   * @param {Matrix } value  A matrix
	   * @param {number} column  The index of the column
	   * @return {Matrix}        The retrieved column
	   */
	  function _column(value, column) {
	    // check dimensions
	    if (value.size().length !== 2) {
	      throw new Error('Only two dimensional matrix is supported');
	    }
	    (0, _array.validateIndex)(column, value.size()[1]);
	    const rowRange = range(0, value.size()[0]);
	    const index = new Index(rowRange, [column]);
	    const result = value.subset(index);
	    // once config.legacySubset just return result
	    return (0, _is.isMatrix)(result) ? result : matrix([[result]]);
	  }
	});
	return column$1;
}

var count$1 = {};

var hasRequiredCount$1;

function requireCount$1 () {
	if (hasRequiredCount$1) return count$1;
	hasRequiredCount$1 = 1;

	Object.defineProperty(count$1, "__esModule", {
	  value: true
	});
	count$1.createCount = void 0;
	var _factory = requireFactory$b();
	const name = 'count';
	const dependencies = ['typed', 'size', 'prod'];
	count$1.createCount = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    prod
	  } = _ref;
	  /**
	   * Count the number of elements of a matrix, array or string.
	   *
	   * Syntax:
	   *
	   *     math.count(x)
	   *
	   * Examples:
	   *
	   *     math.count('hello world')        // returns 11
	   *     const A = [[1, 2, 3], [4, 5, 6]]
	   *     math.count(A)                    // returns 6
	   *     math.count(math.range(1,6))      // returns 5
	   *
	   * See also:
	   *
	   *     size
	   *
	   * @param {string | Array | Matrix} x  A matrix or string
	   * @return {number} An integer with the elements in `x`.
	   */
	  return typed(name, {
	    string: function (x) {
	      return x.length;
	    },
	    'Matrix | Array': function (x) {
	      return prod(size(x));
	    }
	  });
	});
	return count$1;
}

var cross$1 = {};

var hasRequiredCross$1;

function requireCross$1 () {
	if (hasRequiredCross$1) return cross$1;
	hasRequiredCross$1 = 1;

	Object.defineProperty(cross$1, "__esModule", {
	  value: true
	});
	cross$1.createCross = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'cross';
	const dependencies = ['typed', 'matrix', 'subtract', 'multiply'];
	cross$1.createCross = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    subtract,
	    multiply
	  } = _ref;
	  /**
	   * Calculate the cross product for two vectors in three dimensional space.
	   * The cross product of `A = [a1, a2, a3]` and `B = [b1, b2, b3]` is defined
	   * as:
	   *
	   *    cross(A, B) = [
	   *      a2 * b3 - a3 * b2,
	   *      a3 * b1 - a1 * b3,
	   *      a1 * b2 - a2 * b1
	   *    ]
	   *
	   * If one of the input vectors has a dimension greater than 1, the output
	   * vector will be a 1x3 (2-dimensional) matrix.
	   *
	   * Syntax:
	   *
	   *    math.cross(x, y)
	   *
	   * Examples:
	   *
	   *    math.cross([1, 1, 0],   [0, 1, 1])       // Returns [1, -1, 1]
	   *    math.cross([3, -3, 1],  [4, 9, 2])       // Returns [-15, -2, 39]
	   *    math.cross([2, 3, 4],   [5, 6, 7])       // Returns [-3, 6, -3]
	   *    math.cross([[1, 2, 3]], [[4], [5], [6]]) // Returns [[-3, 6, -3]]
	   *
	   * See also:
	   *
	   *    dot, multiply
	   *
	   * @param  {Array | Matrix} x   First vector
	   * @param  {Array | Matrix} y   Second vector
	   * @return {Array | Matrix}     Returns the cross product of `x` and `y`
	   */
	  return typed(name, {
	    'Matrix, Matrix': function (x, y) {
	      return matrix(_cross(x.toArray(), y.toArray()));
	    },
	    'Matrix, Array': function (x, y) {
	      return matrix(_cross(x.toArray(), y));
	    },
	    'Array, Matrix': function (x, y) {
	      return matrix(_cross(x, y.toArray()));
	    },
	    'Array, Array': _cross
	  });

	  /**
	   * Calculate the cross product for two arrays
	   * @param {Array} x  First vector
	   * @param {Array} y  Second vector
	   * @returns {Array} Returns the cross product of x and y
	   * @private
	   */
	  function _cross(x, y) {
	    const highestDimension = Math.max((0, _array.arraySize)(x).length, (0, _array.arraySize)(y).length);
	    x = (0, _array.squeeze)(x);
	    y = (0, _array.squeeze)(y);
	    const xSize = (0, _array.arraySize)(x);
	    const ySize = (0, _array.arraySize)(y);
	    if (xSize.length !== 1 || ySize.length !== 1 || xSize[0] !== 3 || ySize[0] !== 3) {
	      throw new RangeError('Vectors with length 3 expected ' + '(Size A = [' + xSize.join(', ') + '], B = [' + ySize.join(', ') + '])');
	    }
	    const product = [subtract(multiply(x[1], y[2]), multiply(x[2], y[1])), subtract(multiply(x[2], y[0]), multiply(x[0], y[2])), subtract(multiply(x[0], y[1]), multiply(x[1], y[0]))];
	    if (highestDimension > 1) {
	      return [product];
	    } else {
	      return product;
	    }
	  }
	});
	return cross$1;
}

var diag$1 = {};

var hasRequiredDiag$1;

function requireDiag$1 () {
	if (hasRequiredDiag$1) return diag$1;
	hasRequiredDiag$1 = 1;

	Object.defineProperty(diag$1, "__esModule", {
	  value: true
	});
	diag$1.createDiag = void 0;
	var _is = requireIs();
	var _array = requireArray();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	const name = 'diag';
	const dependencies = ['typed', 'matrix', 'DenseMatrix', 'SparseMatrix'];
	diag$1.createDiag = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    DenseMatrix,
	    SparseMatrix
	  } = _ref;
	  /**
	   * Create a diagonal matrix or retrieve the diagonal of a matrix
	   *
	   * When `x` is a vector, a matrix with vector `x` on the diagonal will be returned.
	   * When `x` is a two dimensional matrix, the matrixes `k`th diagonal will be returned as vector.
	   * When k is positive, the values are placed on the super diagonal.
	   * When k is negative, the values are placed on the sub diagonal.
	   *
	   * Syntax:
	   *
	   *     math.diag(X)
	   *     math.diag(X, format)
	   *     math.diag(X, k)
	   *     math.diag(X, k, format)
	   *
	   * Examples:
	   *
	   *     // create a diagonal matrix
	   *     math.diag([1, 2, 3])      // returns [[1, 0, 0], [0, 2, 0], [0, 0, 3]]
	   *     math.diag([1, 2, 3], 1)   // returns [[0, 1, 0, 0], [0, 0, 2, 0], [0, 0, 0, 3]]
	   *     math.diag([1, 2, 3], -1)  // returns [[0, 0, 0], [1, 0, 0], [0, 2, 0], [0, 0, 3]]
	   *
	   *    // retrieve the diagonal from a matrix
	   *    const a = [[1, 2, 3], [4, 5, 6], [7, 8, 9]]
	   *    math.diag(a)   // returns [1, 5, 9]
	   *
	   * See also:
	   *
	   *     ones, zeros, identity
	   *
	   * @param {Matrix | Array} x          A two dimensional matrix or a vector
	   * @param {number | BigNumber} [k=0]  The diagonal where the vector will be filled
	   *                                    in or retrieved.
	   * @param {string} [format='dense']   The matrix storage format.
	   *
	   * @returns {Matrix | Array} Diagonal matrix from input vector, or diagonal from input matrix.
	   */
	  return typed(name, {
	    // FIXME: simplify this huge amount of signatures as soon as typed-function supports optional arguments

	    Array: function (x) {
	      return _diag(x, 0, (0, _array.arraySize)(x), null);
	    },
	    'Array, number': function (x, k) {
	      return _diag(x, k, (0, _array.arraySize)(x), null);
	    },
	    'Array, BigNumber': function (x, k) {
	      return _diag(x, k.toNumber(), (0, _array.arraySize)(x), null);
	    },
	    'Array, string': function (x, format) {
	      return _diag(x, 0, (0, _array.arraySize)(x), format);
	    },
	    'Array, number, string': function (x, k, format) {
	      return _diag(x, k, (0, _array.arraySize)(x), format);
	    },
	    'Array, BigNumber, string': function (x, k, format) {
	      return _diag(x, k.toNumber(), (0, _array.arraySize)(x), format);
	    },
	    Matrix: function (x) {
	      return _diag(x, 0, x.size(), x.storage());
	    },
	    'Matrix, number': function (x, k) {
	      return _diag(x, k, x.size(), x.storage());
	    },
	    'Matrix, BigNumber': function (x, k) {
	      return _diag(x, k.toNumber(), x.size(), x.storage());
	    },
	    'Matrix, string': function (x, format) {
	      return _diag(x, 0, x.size(), format);
	    },
	    'Matrix, number, string': function (x, k, format) {
	      return _diag(x, k, x.size(), format);
	    },
	    'Matrix, BigNumber, string': function (x, k, format) {
	      return _diag(x, k.toNumber(), x.size(), format);
	    }
	  });

	  /**
	   * Creeate diagonal matrix from a vector or vice versa
	   * @param {Array | Matrix} x
	   * @param {number} k
	   * @param {string} format Storage format for matrix. If null,
	   *                          an Array is returned
	   * @returns {Array | Matrix}
	   * @private
	   */
	  function _diag(x, k, size, format) {
	    if (!(0, _number.isInteger)(k)) {
	      throw new TypeError('Second parameter in function diag must be an integer');
	    }
	    const kSuper = k > 0 ? k : 0;
	    const kSub = k < 0 ? -k : 0;

	    // check dimensions
	    switch (size.length) {
	      case 1:
	        return _createDiagonalMatrix(x, k, format, size[0], kSub, kSuper);
	      case 2:
	        return _getDiagonal(x, k, format, size, kSub, kSuper);
	    }
	    throw new RangeError('Matrix for function diag must be 2 dimensional');
	  }
	  function _createDiagonalMatrix(x, k, format, l, kSub, kSuper) {
	    // matrix size
	    const ms = [l + kSub, l + kSuper];
	    if (format && format !== 'sparse' && format !== 'dense') {
	      throw new TypeError(`Unknown matrix type ${format}"`);
	    }

	    // create diagonal matrix
	    const m = format === 'sparse' ? SparseMatrix.diagonal(ms, x, k) : DenseMatrix.diagonal(ms, x, k);
	    // check we need to return a matrix
	    return format !== null ? m : m.valueOf();
	  }
	  function _getDiagonal(x, k, format, s, kSub, kSuper) {
	    // check x is a Matrix
	    if ((0, _is.isMatrix)(x)) {
	      // get diagonal matrix
	      const dm = x.diagonal(k);
	      // check we need to return a matrix
	      if (format !== null) {
	        // check we need to change matrix format
	        if (format !== dm.storage()) {
	          return matrix(dm, format);
	        }
	        return dm;
	      }
	      return dm.valueOf();
	    }
	    // vector size
	    const n = Math.min(s[0] - kSub, s[1] - kSuper);
	    // diagonal values
	    const vector = [];
	    // loop diagonal
	    for (let i = 0; i < n; i++) {
	      vector[i] = x[i + kSub][i + kSuper];
	    }
	    // check we need to return a matrix
	    return format !== null ? matrix(vector) : vector;
	  }
	});
	return diag$1;
}

var filter$1 = {};

var hasRequiredFilter$1;

function requireFilter$1 () {
	if (hasRequiredFilter$1) return filter$1;
	hasRequiredFilter$1 = 1;

	Object.defineProperty(filter$1, "__esModule", {
	  value: true
	});
	filter$1.createFilter = void 0;
	var _optimizeCallback = requireOptimizeCallback();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'filter';
	const dependencies = ['typed'];
	filter$1.createFilter = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Filter the items in an array or one dimensional matrix.
	   *
	   * The callback is invoked with three arguments: the current value,
	   * the current index, and the matrix operated upon.
	   * Note that because the matrix/array might be
	   * multidimensional, the "index" argument is always an array of numbers giving
	   * the index in each dimension. This is true even for vectors: the "index"
	   * argument is an array of length 1, rather than simply a number.
	   *
	   * Syntax:
	   *
	   *    math.filter(x, test)
	   *
	   * Examples:
	   *
	   *    function isPositive (x) {
	   *      return x > 0
	   *    }
	   *    math.filter([6, -2, -1, 4, 3], isPositive) // returns [6, 4, 3]
	   *
	   *    math.filter(["23", "foo", "100", "55", "bar"], /[0-9]+/) // returns ["23", "100", "55"]
	   *
	   * See also:
	   *
	   *    forEach, map, sort
	   *
	   * @param {Matrix | Array} x    A one dimensional matrix or array to filter
	   * @param {Function | RegExp} test
	   *        A function or regular expression to test items.
	   *        All entries for which `test` returns true are returned.
	   *        When `test` is a function, it is invoked with three parameters:
	   *        the value of the element, the index of the element, and the
	   *        matrix/array being traversed. The function must return a boolean.
	   * @return {Matrix | Array} Returns the filtered matrix.
	   */
	  return typed('filter', {
	    'Array, function': _filterCallback,
	    'Matrix, function': function (x, test) {
	      return x.create(_filterCallback(x.valueOf(), test), x.datatype());
	    },
	    'Array, RegExp': _array.filterRegExp,
	    'Matrix, RegExp': function (x, test) {
	      return x.create((0, _array.filterRegExp)(x.valueOf(), test), x.datatype());
	    }
	  });
	});

	/**
	 * Filter values in a callback given a callback function
	 * @param {Array} x
	 * @param {Function} callback
	 * @return {Array} Returns the filtered array
	 * @private
	 */
	function _filterCallback(x, callback) {
	  const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, x, 'filter');
	  if (fastCallback.isUnary) {
	    return (0, _array.filter)(x, fastCallback.fn);
	  }
	  return (0, _array.filter)(x, function (value, index, array) {
	    // invoke the callback function with the right number of arguments
	    return fastCallback.fn(value, [index], array);
	  });
	}
	return filter$1;
}

var flatten$1 = {};

var hasRequiredFlatten$1;

function requireFlatten$1 () {
	if (hasRequiredFlatten$1) return flatten$1;
	hasRequiredFlatten$1 = 1;

	Object.defineProperty(flatten$1, "__esModule", {
	  value: true
	});
	flatten$1.createFlatten = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'flatten';
	const dependencies = ['typed'];
	flatten$1.createFlatten = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Flatten a multidimensional matrix into a single dimensional matrix.
	   * A new matrix is returned, the original matrix is left untouched.
	   *
	   * Syntax:
	   *
	   *    math.flatten(x)
	   *
	   * Examples:
	   *
	   *    math.flatten([[1,2], [3,4]])   // returns [1, 2, 3, 4]
	   *
	   * See also:
	   *
	   *    concat, resize, size, squeeze
	   *
	   * @param {DenseMatrix | Array} x   Matrix to be flattened
	   * @return {DenseMatrix | Array} Returns the flattened matrix
	   */
	  return typed(name, {
	    Array: function (x) {
	      return (0, _array.flatten)(x);
	    },
	    DenseMatrix: function (x) {
	      // Return the same matrix type as x (Dense or Sparse Matrix)
	      // Return the same data type as x
	      return x.create((0, _array.flatten)(x.valueOf(), true), x.datatype());
	    },
	    SparseMatrix: function (_x) {
	      throw new TypeError('SparseMatrix is not supported by function flatten ' + 'because it does not support 1D vectors. ' + 'Convert to a DenseMatrix or Array first. Example: flatten(x.toArray())');
	    }
	  });
	});
	return flatten$1;
}

var forEach$2 = {};

var hasRequiredForEach$1;

function requireForEach$1 () {
	if (hasRequiredForEach$1) return forEach$2;
	hasRequiredForEach$1 = 1;

	Object.defineProperty(forEach$2, "__esModule", {
	  value: true
	});
	forEach$2.createForEach = void 0;
	var _optimizeCallback = requireOptimizeCallback();
	var _factory = requireFactory$b();
	var _array = requireArray();
	const name = 'forEach';
	const dependencies = ['typed'];
	forEach$2.createForEach = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Iterate over all elements of a matrix/array, and executes the given callback function.
	   *
	   * The callback is invoked with three arguments: the current value,
	   * the current index, and the matrix operated upon.
	   * Note that because the matrix/array might be
	   * multidimensional, the "index" argument is always an array of numbers giving
	   * the index in each dimension. This is true even for vectors: the "index"
	   * argument is an array of length 1, rather than simply a number.
	   *
	   * Syntax:
	   *
	   *    math.forEach(x, callback)
	   *
	   * Examples:
	   *
	   *    math.forEach([1, 2, 3], function(value) {
	   *      console.log(value)
	   *    })
	   *    // outputs 1, 2, 3
	   *
	   * See also:
	   *
	   *    filter, map, sort
	   *
	   * @param {Matrix | Array} x    The matrix to iterate on.
	   * @param {Function} callback   The callback function is invoked with three
	   *                              parameters: the value of the element, the index
	   *                              of the element, and the Matrix/array being traversed.
	   */
	  return typed(name, {
	    'Array, function': _forEach,
	    'Matrix, function': function (x, callback) {
	      x.forEach(callback);
	    }
	  });
	});

	/**
	 * forEach for a multidimensional array
	 * @param {Array} array
	 * @param {Function} callback
	 * @private
	 */
	function _forEach(array, callback) {
	  const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, array, name);
	  (0, _array.deepForEach)(array, fastCallback.fn, fastCallback.isUnary);
	}
	return forEach$2;
}

var getMatrixDataType$1 = {};

var hasRequiredGetMatrixDataType$1;

function requireGetMatrixDataType$1 () {
	if (hasRequiredGetMatrixDataType$1) return getMatrixDataType$1;
	hasRequiredGetMatrixDataType$1 = 1;

	Object.defineProperty(getMatrixDataType$1, "__esModule", {
	  value: true
	});
	getMatrixDataType$1.createGetMatrixDataType = void 0;
	var _factory = requireFactory$b();
	var _array = requireArray();
	var _is = requireIs();
	const name = 'getMatrixDataType';
	const dependencies = ['typed'];
	getMatrixDataType$1.createGetMatrixDataType = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Find the data type of all elements in a matrix or array,
	   * for example 'number' if all items are a number and 'Complex' if all values
	   * are complex numbers.
	   * If a matrix contains more than one data type, it will return 'mixed'.
	   *
	   * Syntax:
	   *
	   *    math.getMatrixDataType(x)
	   *
	   * Examples:
	   *
	   *    const x = [ [1, 2, 3], [4, 5, 6] ]
	   *    const mixedX = [ [1, true], [2, 3] ]
	   *    const fractionX = [ [math.fraction(1, 3)], [math.fraction(1, 3)] ]
	   *    const unitX = [ [math.unit('5cm')], [math.unit('5cm')] ]
	   *    const bigNumberX = [ [math.bignumber(1)], [math.bignumber(0)] ]
	   *    const sparse = math.sparse(x)
	   *    const dense = math.matrix(x)
	   *    math.getMatrixDataType(x)   // returns 'number'
	   *    math.getMatrixDataType(sparse)   // returns 'number'
	   *    math.getMatrixDataType(dense)   // returns 'number'
	   *    math.getMatrixDataType(mixedX) // returns 'mixed'
	   *    math.getMatrixDataType(fractionX) // returns 'Fraction'
	   *    math.getMatrixDataType(unitX) // returns 'Unit'
	   *    math.getMatrixDataType(bigNumberX) // return 'BigNumber'
	   *
	   * See also:
	   *  SparseMatrix, DenseMatrix
	   *
	   * @param {...Matrix | Array} x   The Matrix with values.
	   *
	   * @return {string} A string representation of the matrix type
	   */
	  return typed(name, {
	    Array: function (x) {
	      return (0, _array.getArrayDataType)(x, _is.typeOf);
	    },
	    Matrix: function (x) {
	      return x.getDataType();
	    }
	  });
	});
	return getMatrixDataType$1;
}

var identity$1 = {};

var hasRequiredIdentity$1;

function requireIdentity$1 () {
	if (hasRequiredIdentity$1) return identity$1;
	hasRequiredIdentity$1 = 1;

	Object.defineProperty(identity$1, "__esModule", {
	  value: true
	});
	identity$1.createIdentity = void 0;
	var _is = requireIs();
	var _array = requireArray();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	const name = 'identity';
	const dependencies = ['typed', 'config', 'matrix', 'BigNumber', 'DenseMatrix', 'SparseMatrix'];
	identity$1.createIdentity = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    BigNumber,
	    DenseMatrix,
	    SparseMatrix
	  } = _ref;
	  /**
	   * Create a 2-dimensional identity matrix with size m x n or n x n.
	   * The matrix has ones on the diagonal and zeros elsewhere.
	   *
	   * Syntax:
	   *
	   *    math.identity(n)
	   *    math.identity(n, format)
	   *    math.identity(m, n)
	   *    math.identity(m, n, format)
	   *    math.identity([m, n])
	   *    math.identity([m, n], format)
	   *
	   * Examples:
	   *
	   *    math.identity(3)                    // returns [[1, 0, 0], [0, 1, 0], [0, 0, 1]]
	   *    math.identity(3, 2)                 // returns [[1, 0], [0, 1], [0, 0]]
	   *
	   *    const A = [[1, 2, 3], [4, 5, 6]]
	   *    math.identity(math.size(A))         // returns [[1, 0, 0], [0, 1, 0]]
	   *
	   * See also:
	   *
	   *    diag, ones, zeros, size, range
	   *
	   * @param {...number | Matrix | Array} size   The size for the matrix
	   * @param {string} [format]                   The Matrix storage format
	   *
	   * @return {Matrix | Array | number} A matrix with ones on the diagonal.
	   */
	  return typed(name, {
	    '': function () {
	      return config.matrix === 'Matrix' ? matrix([]) : [];
	    },
	    string: function (format) {
	      return matrix(format);
	    },
	    'number | BigNumber': function (rows) {
	      return _identity(rows, rows, config.matrix === 'Matrix' ? 'dense' : undefined);
	    },
	    'number | BigNumber, string': function (rows, format) {
	      return _identity(rows, rows, format);
	    },
	    'number | BigNumber, number | BigNumber': function (rows, cols) {
	      return _identity(rows, cols, config.matrix === 'Matrix' ? 'dense' : undefined);
	    },
	    'number | BigNumber, number | BigNumber, string': function (rows, cols, format) {
	      return _identity(rows, cols, format);
	    },
	    Array: function (size) {
	      return _identityVector(size);
	    },
	    'Array, string': function (size, format) {
	      return _identityVector(size, format);
	    },
	    Matrix: function (size) {
	      return _identityVector(size.valueOf(), size.storage());
	    },
	    'Matrix, string': function (size, format) {
	      return _identityVector(size.valueOf(), format);
	    }
	  });
	  function _identityVector(size, format) {
	    switch (size.length) {
	      case 0:
	        return format ? matrix(format) : [];
	      case 1:
	        return _identity(size[0], size[0], format);
	      case 2:
	        return _identity(size[0], size[1], format);
	      default:
	        throw new Error('Vector containing two values expected');
	    }
	  }

	  /**
	   * Create an identity matrix
	   * @param {number | BigNumber} rows
	   * @param {number | BigNumber} cols
	   * @param {string} [format]
	   * @returns {Matrix}
	   * @private
	   */
	  function _identity(rows, cols, format) {
	    // BigNumber constructor with the right precision
	    const Big = (0, _is.isBigNumber)(rows) || (0, _is.isBigNumber)(cols) ? BigNumber : null;
	    if ((0, _is.isBigNumber)(rows)) rows = rows.toNumber();
	    if ((0, _is.isBigNumber)(cols)) cols = cols.toNumber();
	    if (!(0, _number.isInteger)(rows) || rows < 1) {
	      throw new Error('Parameters in function identity must be positive integers');
	    }
	    if (!(0, _number.isInteger)(cols) || cols < 1) {
	      throw new Error('Parameters in function identity must be positive integers');
	    }
	    const one = Big ? new BigNumber(1) : 1;
	    const defaultValue = Big ? new Big(0) : 0;
	    const size = [rows, cols];

	    // check we need to return a matrix
	    if (format) {
	      // create diagonal matrix (use optimized implementation for storage format)
	      if (format === 'sparse') {
	        return SparseMatrix.diagonal(size, one, 0, defaultValue);
	      }
	      if (format === 'dense') {
	        return DenseMatrix.diagonal(size, one, 0, defaultValue);
	      }
	      throw new TypeError(`Unknown matrix type "${format}"`);
	    }

	    // create and resize array
	    const res = (0, _array.resize)([], size, defaultValue);
	    // fill in ones on the diagonal
	    const minimum = rows < cols ? rows : cols;
	    // fill diagonal
	    for (let d = 0; d < minimum; d++) {
	      res[d][d] = one;
	    }
	    return res;
	  }
	});
	return identity$1;
}

var kron$1 = {};

var hasRequiredKron$1;

function requireKron$1 () {
	if (hasRequiredKron$1) return kron$1;
	hasRequiredKron$1 = 1;

	Object.defineProperty(kron$1, "__esModule", {
	  value: true
	});
	kron$1.createKron = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'kron';
	const dependencies = ['typed', 'matrix', 'multiplyScalar'];
	kron$1.createKron = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    multiplyScalar
	  } = _ref;
	  /**
	     * Calculates the Kronecker product of 2 matrices or vectors.
	     *
	     * NOTE: If a one dimensional vector / matrix is given, it will be
	     * wrapped so its two dimensions.
	     * See the examples.
	     *
	     * Syntax:
	     *
	     *    math.kron(x, y)
	     *
	     * Examples:
	     *
	     *    math.kron([[1, 0], [0, 1]], [[1, 2], [3, 4]])
	     *    // returns [ [ 1, 2, 0, 0 ], [ 3, 4, 0, 0 ], [ 0, 0, 1, 2 ], [ 0, 0, 3, 4 ] ]
	     *
	     *    math.kron([1,1], [2,3,4])
	     *    // returns [2, 3, 4, 2, 3, 4]
	     *
	     * See also:
	     *
	     *    multiply, dot, cross
	     *
	     * @param  {Array | Matrix} x     First vector
	     * @param  {Array | Matrix} y     Second vector
	     * @return {Array | Matrix}       Returns the Kronecker product of `x` and `y`
	     */
	  return typed(name, {
	    'Matrix, Matrix': function (x, y) {
	      return matrix(_kron(x.toArray(), y.toArray()));
	    },
	    'Matrix, Array': function (x, y) {
	      return matrix(_kron(x.toArray(), y));
	    },
	    'Array, Matrix': function (x, y) {
	      return matrix(_kron(x, y.toArray()));
	    },
	    'Array, Array': _kron
	  });

	  /**
	   * Calculate the Kronecker product of two (1-dimensional) vectors,
	   * with no dimension checking
	   * @param {Array} a  First vector
	   * @param {Array} b  Second vector
	   * @returns {Array}  the 1-dimensional Kronecker product of a and b
	   * @private
	   */
	  function _kron1d(a, b) {
	    // TODO in core overhaul: would be faster to see if we can choose a
	    // particular implementation of multiplyScalar at the beginning,
	    // rather than re-dispatch for _every_ ordered pair of entries.
	    return a.flatMap(x => b.map(y => multiplyScalar(x, y)));
	  }

	  /**
	   * Calculate the Kronecker product of two possibly multidimensional arrays
	   * @param {Array} a  First array
	   * @param {Array} b  Second array
	   * @param {number} [d]  common dimension; if missing, compute and match args
	   * @returns {Array} Returns the Kronecker product of x and y
	   * @private
	     */
	  function _kron(a, b) {
	    let d = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : -1;
	    if (d < 0) {
	      let adim = (0, _array.arraySize)(a).length;
	      let bdim = (0, _array.arraySize)(b).length;
	      d = Math.max(adim, bdim);
	      while (adim++ < d) a = [a];
	      while (bdim++ < d) b = [b];
	    }
	    if (d === 1) return _kron1d(a, b);
	    return a.flatMap(aSlice => b.map(bSlice => _kron(aSlice, bSlice, d - 1)));
	  }
	});
	return kron$1;
}

var map$1 = {};

var hasRequiredMap$1;

function requireMap$1 () {
	if (hasRequiredMap$1) return map$1;
	hasRequiredMap$1 = 1;

	Object.defineProperty(map$1, "__esModule", {
	  value: true
	});
	map$1.createMap = void 0;
	var _optimizeCallback = requireOptimizeCallback();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'map';
	const dependencies = ['typed'];
	map$1.createMap = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Create a new matrix or array with the results of a callback function executed on
	   * each entry of a given matrix/array.
	   *
	   * For each entry of the input,
	   *
	   * the callback is invoked with 2N + 1 arguments:
	   * the N values of the entry, the index at which that entry occurs, and the N full
	   * broadcasted matrix/array being traversed where N is the number of matrices being traversed.
	   * Note that because the matrix/array might be
	   * multidimensional, the "index" argument is always an array of numbers giving
	   * the index in each dimension. This is true even for vectors: the "index"
	   * argument is an array of length 1, rather than simply a number.
	   *
	   * Syntax:
	   *
	   *    math.map(x, callback)
	   *    math.map(x, y, ..., callback)
	   *
	   * Examples:
	   *
	   *    math.map([1, 2, 3], function(value) {
	   *      return value * value
	   *    })  // returns [1, 4, 9]
	   *    math.map([1, 2], [3, 4], function(a, b) {
	   *     return a + b
	   *    })  // returns [4, 6]
	   *
	   *    // The callback is normally called with three arguments:
	   *    //    callback(value, index, Array)
	   *    // If you want to call with only one argument, use:
	   *    math.map([1, 2, 3], x => math.format(x)) // returns ['1', '2', '3']
	   *    // It can also be called with 2N + 1 arguments: for N arrays
	   *    //    callback(value1, value2, index, BroadcastedArray1, BroadcastedArray2)
	   *
	   * History:
	   *
	   *    v0.13  Created
	   *    v1.1   Clone the indices on each callback in case callback mutates
	   *    v13.1  Support multiple inputs to the callback
	   *
	   * See also:
	   *
	   *    filter, forEach, sort
	   *
	   * @param {Matrix | Array} x    The input to iterate on.
	   * @param {Function} callback
	   *     The function to call (as described above) on each entry of the input
	   * @return {Matrix | array}
	   *     Transformed map of x; always has the same type and shape as x
	   */
	  return typed(name, {
	    'Array, function': _mapArray,
	    'Matrix, function': function (x, callback) {
	      return x.map(callback);
	    },
	    'Array|Matrix, Array|Matrix, ...Array|Matrix|function': (A, B, rest) => _mapMultiple([A, B, ...rest.slice(0, rest.length - 1)], rest[rest.length - 1])
	  });

	  /**
	  * Maps over multiple arrays or matrices.
	  *
	  * @param {Array<Array|Matrix>} Arrays - An array of arrays or matrices to map over.
	  * @param {function} multiCallback - The callback function to apply to each element.
	  * @throws {Error} If the last argument is not a callback function.
	  * @returns {Array|Matrix} A new array or matrix with each element being the result of the callback function.
	  *
	  * @example
	  * _mapMultiple([[1, 2, 3], [4, 5, 6]], (a, b) => a + b); // Returns [5, 7, 9]
	  */
	  function _mapMultiple(Arrays, multiCallback) {
	    if (typeof multiCallback !== 'function') {
	      throw new Error('Last argument must be a callback function');
	    }
	    const firstArrayIsMatrix = Arrays[0].isMatrix;
	    const sizes = Arrays.map(M => M.isMatrix ? M.size() : (0, _array.arraySize)(M));
	    const newSize = (0, _array.broadcastSizes)(...sizes);
	    const numberOfArrays = Arrays.length;
	    const _get = firstArrayIsMatrix ? (matrix, idx) => matrix.get(idx) : _array.get;
	    const firstValues = Arrays.map((collection, i) => {
	      const firstIndex = sizes[i].map(() => 0);
	      return collection.isMatrix ? collection.get(firstIndex) : (0, _array.get)(collection, firstIndex);
	    });
	    const callbackArgCount = typed.isTypedFunction(multiCallback) ? _getTypedCallbackArgCount(multiCallback, firstValues, newSize.map(() => 0), Arrays) : _getCallbackArgCount(multiCallback, numberOfArrays);
	    if (callbackArgCount < 2) {
	      const callback = _getLimitedCallback(callbackArgCount, multiCallback, null);
	      return mapMultiple(Arrays, callback);
	    }
	    const broadcastedArrays = firstArrayIsMatrix ? Arrays.map(M => M.isMatrix ? M.create((0, _array.broadcastTo)(M.toArray(), newSize), M.datatype()) : Arrays[0].create((0, _array.broadcastTo)(M.valueOf(), newSize))) : Arrays.map(M => M.isMatrix ? (0, _array.broadcastTo)(M.toArray(), newSize) : (0, _array.broadcastTo)(M, newSize));
	    const callback = _getLimitedCallback(callbackArgCount, multiCallback, broadcastedArrays);
	    const broadcastedArraysCallback = (x, idx) => callback([x, ...broadcastedArrays.slice(1).map(array => _get(array, idx))], idx);
	    if (firstArrayIsMatrix) {
	      return broadcastedArrays[0].map(broadcastedArraysCallback);
	    } else {
	      return _mapArray(broadcastedArrays[0], broadcastedArraysCallback);
	    }
	  }
	  function mapMultiple(collections, callback) {
	    // collections can be matrices or arrays
	    // callback must be a function of the form (collections, [index])
	    const firstCollection = collections[0];
	    const arrays = collections.map(collection => collection.isMatrix ? collection.valueOf() : collection);
	    const sizes = collections.map(collection => collection.isMatrix ? collection.size() : (0, _array.arraySize)(collection));
	    const finalSize = (0, _array.broadcastSizes)(...sizes);
	    // the offset means for each initial array, how much smaller is it than the final size
	    const offsets = sizes.map(size => finalSize.length - size.length);
	    const maxDepth = finalSize.length - 1;
	    const callbackUsesIndex = callback.length > 1;
	    const index = callbackUsesIndex ? [] : null;
	    const resultsArray = iterate(arrays, 0);
	    if (firstCollection.isMatrix) {
	      const resultsMatrix = firstCollection.create();
	      resultsMatrix._data = resultsArray;
	      resultsMatrix._size = finalSize;
	      return resultsMatrix;
	    } else {
	      return resultsArray;
	    }
	    function iterate(arrays) {
	      let depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
	      // each array can have different sizes
	      const currentDimensionSize = finalSize[depth];
	      const result = Array(currentDimensionSize);
	      if (depth < maxDepth) {
	        for (let i = 0; i < currentDimensionSize; i++) {
	          if (index) index[depth] = i;
	          // if there is an offset greater than the current dimension
	          // pass the array, if the size of the array is 1 pass the first
	          // element of the array
	          result[i] = iterate(arrays.map((array, arrayIndex) => offsets[arrayIndex] > depth ? array : array.length === 1 ? array[0] : array[i]), depth + 1);
	        }
	      } else {
	        for (let i = 0; i < currentDimensionSize; i++) {
	          if (index) index[depth] = i;
	          result[i] = callback(arrays.map(a => a.length === 1 ? a[0] : a[i]), index ? index.slice() : undefined);
	        }
	      }
	      return result;
	    }
	  }

	  /**
	    * Creates a limited callback based on the argument pattern.
	    * @param {number} callbackArgCount - The argument pattern (0, 1, or 2)
	    * @param {Function} multiCallback - The original callback function
	    * @param {Array} broadcastedArrays - The broadcasted arrays (for case 2)
	    * @returns {Function} The limited callback function
	    */
	  function _getLimitedCallback(callbackArgCount, multiCallback, broadcastedArrays) {
	    switch (callbackArgCount) {
	      case 0:
	        return x => multiCallback(...x);
	      case 1:
	        return (x, idx) => multiCallback(...x, idx);
	      case 2:
	        return (x, idx) => multiCallback(...x, idx, ...broadcastedArrays);
	    }
	  }

	  /**
	     * Determines the argument pattern of a regular callback function.
	     * @param {Function} callback - The callback function to analyze
	     * @param {number} numberOfArrays - Number of arrays being processed
	     * @returns {number} 0 = values only, 1 = values + index, 2 = values + index + arrays
	     */
	  function _getCallbackArgCount(callback, numberOfArrays) {
	    const callbackStr = callback.toString();
	    // Check if the callback function uses `arguments`
	    if (/arguments/.test(callbackStr)) return 2;

	    // Extract the parameters of the callback function
	    const paramsStr = callbackStr.match(/\(.*?\)/);
	    // Check if the callback function uses rest parameters
	    if (/\.\.\./.test(paramsStr)) return 2;
	    if (callback.length > numberOfArrays + 1) {
	      return 2;
	    }
	    if (callback.length === numberOfArrays + 1) {
	      return 1;
	    }
	    return 0;
	  }

	  /**
	  * Determines the argument pattern of a typed callback function.
	  * @param {Function} callback - The typed callback function to analyze
	  * @param {Array} values - Sample values for signature resolution
	  * @param {Array} idx - Sample index for signature resolution
	  * @param {Array} arrays - Sample arrays for signature resolution
	  * @returns {number} 0 = values only, 1 = values + index, 2 = values + index + arrays
	  */

	  function _getTypedCallbackArgCount(callback, values, idx, arrays) {
	    if (typed.resolve(callback, [...values, idx, ...arrays]) !== null) {
	      return 2;
	    }
	    if (typed.resolve(callback, [...values, idx]) !== null) {
	      return 1;
	    }
	    if (typed.resolve(callback, values) !== null) {
	      return 0;
	    }
	    // this should never happen
	    return 0;
	  }
	  /**
	  * Map for a multi dimensional array
	  * @param {Array} array
	  * @param {Function} callback
	  * @return {Array}
	  * @private
	  */
	  function _mapArray(array, callback) {
	    const fastCallback = (0, _optimizeCallback.optimizeCallback)(callback, array, name);
	    return (0, _array.deepMap)(array, fastCallback.fn, fastCallback.isUnary);
	  }
	});
	return map$1;
}

var diff$1 = {};

var hasRequiredDiff$1;

function requireDiff$1 () {
	if (hasRequiredDiff$1) return diff$1;
	hasRequiredDiff$1 = 1;

	Object.defineProperty(diff$1, "__esModule", {
	  value: true
	});
	diff$1.createDiff = void 0;
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _is = requireIs();
	const name = 'diff';
	const dependencies = ['typed', 'matrix', 'subtract', 'number'];
	diff$1.createDiff = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    subtract,
	    number
	  } = _ref;
	  /**
	   * Create a new matrix or array of the difference between elements of the given array
	   * The optional dim parameter lets you specify the dimension to evaluate the difference of
	   * If no dimension parameter is passed it is assumed as dimension 0
	   *
	   * Dimension is zero-based in javascript and one-based in the parser and can be a number or bignumber
	   * Arrays must be 'rectangular' meaning arrays like [1, 2]
	   * If something is passed as a matrix it will be returned as a matrix but other than that all matrices are converted to arrays
	   *
	   * Syntax:
	   *
	   *     math.diff(arr)
	   *     math.diff(arr, dim)
	   *
	   * Examples:
	   *
	   *     const arr = [1, 2, 4, 7, 0]
	   *     math.diff(arr) // returns [1, 2, 3, -7] (no dimension passed so 0 is assumed)
	   *     math.diff(math.matrix(arr)) // returns Matrix [1, 2, 3, -7]
	   *
	   *     const arr = [[1, 2, 3, 4, 5], [1, 2, 3, 4, 5], [9, 8, 7, 6, 4]]
	   *     math.diff(arr) // returns [[0, 0, 0, 0, 0], [8, 6, 4, 2, -1]]
	   *     math.diff(arr, 0) // returns [[0, 0, 0, 0, 0], [8, 6, 4, 2, -1]]
	   *     math.diff(arr, 1) // returns [[1, 1, 1, 1], [1, 1, 1, 1], [-1, -1, -1, -2]]
	   *     math.diff(arr, math.bignumber(1)) // returns [[1, 1, 1, 1], [1, 1, 1, 1], [-1, -1, -1, -2]]
	   *
	   *     math.diff(arr, 2) // throws RangeError as arr is 2 dimensional not 3
	   *     math.diff(arr, -1) // throws RangeError as negative dimensions are not allowed
	   *
	   *     // These will all produce the same result
	   *     math.diff([[1, 2], [3, 4]])
	   *     math.diff([math.matrix([1, 2]), math.matrix([3, 4])])
	   *     math.diff([[1, 2], math.matrix([3, 4])])
	   *     math.diff([math.matrix([1, 2]), [3, 4]])
	   *     // They do not produce the same result as  math.diff(math.matrix([[1, 2], [3, 4]])) as this returns a matrix
	   *
	   * See Also:
	   *
	   *      sum
	   *      subtract
	   *      partitionSelect
	   *
	   * @param {Array | Matrix} arr      An array or matrix
	   * @param {number | BigNumber} dim  Dimension
	   * @return {Array | Matrix}         Difference between array elements in given dimension
	   */
	  return typed(name, {
	    'Array | Matrix': function (arr) {
	      // No dimension specified => assume dimension 0
	      if ((0, _is.isMatrix)(arr)) {
	        return matrix(_diff(arr.toArray()));
	      } else {
	        return _diff(arr);
	      }
	    },
	    'Array | Matrix, number': function (arr, dim) {
	      if (!(0, _number.isInteger)(dim)) throw new RangeError('Dimension must be a whole number');
	      if ((0, _is.isMatrix)(arr)) {
	        return matrix(_recursive(arr.toArray(), dim));
	      } else {
	        return _recursive(arr, dim);
	      }
	    },
	    'Array, BigNumber': typed.referTo('Array,number', selfAn => (arr, dim) => selfAn(arr, number(dim))),
	    'Matrix, BigNumber': typed.referTo('Matrix,number', selfMn => (arr, dim) => selfMn(arr, number(dim)))
	  });

	  /**
	   * Recursively find the correct dimension in the array/matrix
	   * Then Apply _diff to that dimension
	   *
	   * @param {Array} arr      The array
	   * @param {number} dim     Dimension
	   * @return {Array}         resulting array
	   */
	  function _recursive(arr, dim) {
	    if ((0, _is.isMatrix)(arr)) {
	      arr = arr.toArray(); // Makes sure arrays like [ matrix([0, 1]), matrix([1, 0]) ] are processed properly
	    }
	    if (!Array.isArray(arr)) {
	      throw RangeError('Array/Matrix does not have that many dimensions');
	    }
	    if (dim > 0) {
	      const result = [];
	      arr.forEach(element => {
	        result.push(_recursive(element, dim - 1));
	      });
	      return result;
	    } else if (dim === 0) {
	      return _diff(arr);
	    } else {
	      throw RangeError('Cannot have negative dimension');
	    }
	  }

	  /**
	   * Difference between elements in the array
	   *
	   * @param {Array} arr      An array
	   * @return {Array}         resulting array
	   */
	  function _diff(arr) {
	    const result = [];
	    const size = arr.length;
	    for (let i = 1; i < size; i++) {
	      result.push(_ElementDiff(arr[i - 1], arr[i]));
	    }
	    return result;
	  }

	  /**
	   * Difference between 2 objects
	   *
	   * @param {Object} obj1    First object
	   * @param {Object} obj2    Second object
	   * @return {Array}         resulting array
	   */
	  function _ElementDiff(obj1, obj2) {
	    // Convert matrices to arrays
	    if ((0, _is.isMatrix)(obj1)) obj1 = obj1.toArray();
	    if ((0, _is.isMatrix)(obj2)) obj2 = obj2.toArray();
	    const obj1IsArray = Array.isArray(obj1);
	    const obj2IsArray = Array.isArray(obj2);
	    if (obj1IsArray && obj2IsArray) {
	      return _ArrayDiff(obj1, obj2);
	    }
	    if (!obj1IsArray && !obj2IsArray) {
	      return subtract(obj2, obj1); // Difference is (second - first) NOT (first - second)
	    }
	    throw TypeError('Cannot calculate difference between 1 array and 1 non-array');
	  }

	  /**
	   * Difference of elements in 2 arrays
	   *
	   * @param {Array} arr1     Array 1
	   * @param {Array} arr2     Array 2
	   * @return {Array}         resulting array
	   */
	  function _ArrayDiff(arr1, arr2) {
	    if (arr1.length !== arr2.length) {
	      throw RangeError('Not all sub-arrays have the same length');
	    }
	    const result = [];
	    const size = arr1.length;
	    for (let i = 0; i < size; i++) {
	      result.push(_ElementDiff(arr1[i], arr2[i]));
	    }
	    return result;
	  }
	});
	return diff$1;
}

var ones$1 = {};

var hasRequiredOnes$1;

function requireOnes$1 () {
	if (hasRequiredOnes$1) return ones$1;
	hasRequiredOnes$1 = 1;

	Object.defineProperty(ones$1, "__esModule", {
	  value: true
	});
	ones$1.createOnes = void 0;
	var _is = requireIs();
	var _number = requireNumber$3();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'ones';
	const dependencies = ['typed', 'config', 'matrix', 'BigNumber'];
	ones$1.createOnes = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    BigNumber
	  } = _ref;
	  /**
	   * Create a matrix filled with ones. The created matrix can have one or
	   * multiple dimensions.
	   *
	   * Syntax:
	   *
	   *    math.ones(m)
	   *    math.ones(m, format)
	   *    math.ones(m, n)
	   *    math.ones(m, n, format)
	   *    math.ones([m, n])
	   *    math.ones([m, n], format)
	   *    math.ones([m, n, p, ...])
	   *    math.ones([m, n, p, ...], format)
	   *
	   * Examples:
	   *
	   *    math.ones()                    // returns []
	   *    math.ones(3)                   // returns [1, 1, 1]
	   *    math.ones(3, 2)                // returns [[1, 1], [1, 1], [1, 1]]
	   *    math.ones(3, 2, 'dense')       // returns Dense Matrix [[1, 1], [1, 1], [1, 1]]
	   *
	   *    const A = [[1, 2, 3], [4, 5, 6]]
	   *    math.ones(math.size(A))       // returns [[1, 1, 1], [1, 1, 1]]
	   *
	   * See also:
	   *
	   *    zeros, identity, size, range
	   *
	   * @param {...(number|BigNumber) | Array} size    The size of each dimension of the matrix
	   * @param {string} [format]           The Matrix storage format
	   *
	   * @return {Array | Matrix | number}  A matrix filled with ones
	   */
	  return typed('ones', {
	    '': function () {
	      return config.matrix === 'Array' ? _ones([]) : _ones([], 'default');
	    },
	    // math.ones(m, n, p, ..., format)
	    // TODO: more accurate signature '...number | BigNumber, string' as soon as typed-function supports this
	    '...number | BigNumber | string': function (size) {
	      const last = size[size.length - 1];
	      if (typeof last === 'string') {
	        const format = size.pop();
	        return _ones(size, format);
	      } else if (config.matrix === 'Array') {
	        return _ones(size);
	      } else {
	        return _ones(size, 'default');
	      }
	    },
	    Array: _ones,
	    Matrix: function (size) {
	      const format = size.storage();
	      return _ones(size.valueOf(), format);
	    },
	    'Array | Matrix, string': function (size, format) {
	      return _ones(size.valueOf(), format);
	    }
	  });

	  /**
	   * Create an Array or Matrix with ones
	   * @param {Array} size
	   * @param {string} [format='default']
	   * @return {Array | Matrix}
	   * @private
	   */
	  function _ones(size, format) {
	    const hasBigNumbers = _normalize(size);
	    const defaultValue = hasBigNumbers ? new BigNumber(1) : 1;
	    _validate(size);
	    if (format) {
	      // return a matrix
	      const m = matrix(format);
	      if (size.length > 0) {
	        return m.resize(size, defaultValue);
	      }
	      return m;
	    } else {
	      // return an Array
	      const arr = [];
	      if (size.length > 0) {
	        return (0, _array.resize)(arr, size, defaultValue);
	      }
	      return arr;
	    }
	  }

	  // replace BigNumbers with numbers, returns true if size contained BigNumbers
	  function _normalize(size) {
	    let hasBigNumbers = false;
	    size.forEach(function (value, index, arr) {
	      if ((0, _is.isBigNumber)(value)) {
	        hasBigNumbers = true;
	        arr[index] = value.toNumber();
	      }
	    });
	    return hasBigNumbers;
	  }

	  // validate arguments
	  function _validate(size) {
	    size.forEach(function (value) {
	      if (typeof value !== 'number' || !(0, _number.isInteger)(value) || value < 0) {
	        throw new Error('Parameters in function ones must be positive integers');
	      }
	    });
	  }
	});
	return ones$1;
}

var range$1 = {};

var noop$1 = {};

var hasRequiredNoop;

function requireNoop () {
	if (hasRequiredNoop) return noop$1;
	hasRequiredNoop = 1;

	Object.defineProperty(noop$1, "__esModule", {
	  value: true
	});
	noop$1.noBignumber = noBignumber;
	noop$1.noFraction = noFraction;
	noop$1.noIndex = noIndex;
	noop$1.noMatrix = noMatrix;
	noop$1.noSubset = noSubset;
	function noBignumber() {
	  throw new Error('No "bignumber" implementation available');
	}
	function noFraction() {
	  throw new Error('No "fraction" implementation available');
	}
	function noMatrix() {
	  throw new Error('No "matrix" implementation available');
	}
	function noIndex() {
	  throw new Error('No "index" implementation available');
	}
	function noSubset() {
	  throw new Error('No "matrix" implementation available');
	}
	return noop$1;
}

var hasRequiredRange$1;

function requireRange$1 () {
	if (hasRequiredRange$1) return range$1;
	hasRequiredRange$1 = 1;

	Object.defineProperty(range$1, "__esModule", {
	  value: true
	});
	range$1.createRange = void 0;
	var _factory = requireFactory$b();
	var _noop = requireNoop();
	const name = 'range';
	const dependencies = ['typed', 'config', '?matrix', '?bignumber', 'equal', 'smaller', 'smallerEq', 'larger', 'largerEq', 'add', 'isZero', 'isPositive'];
	range$1.createRange = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    bignumber,
	    smaller,
	    smallerEq,
	    larger,
	    largerEq,
	    add,
	    isZero,
	    isPositive
	  } = _ref;
	  /**
	   * Create a matrix or array containing a range of values.
	   * By default, the range end is excluded. This can be customized by providing
	   * an extra parameter `includeEnd`.
	   *
	   * Syntax:
	   *
	   *     math.range(str [, includeEnd])               // Create a range from a string,
	   *                                                  // where the string contains the
	   *                                                  // start, optional step, and end,
	   *                                                  // separated by a colon.
	   *     math.range(start, end [, includeEnd])        // Create a range with start and
	   *                                                  // end and a step size of 1.
	   *     math.range(start, end, step [, includeEnd])  // Create a range with start, step,
	   *                                                  // and end.
	   *
	   * Where:
	   *
	   * - `str: string`
	   *   A string 'start:end' or 'start:step:end'
	   * - `start: {number | bigint | BigNumber | Fraction | Unit}`
	   *   Start of the range
	   * - `end: number | bigint | BigNumber | Fraction | Unit`
	   *   End of the range, excluded by default, included when parameter includeEnd=true
	   * - `step: number | bigint | BigNumber | Fraction | Unit`
	   *   Step size. Default value is 1.
	   * - `includeEnd: boolean`
	   *   Option to specify whether to include the end or not. False by default.
	   *
	   * The function returns a `DenseMatrix` when the library is configured with
	   * `config = { matrix: 'Matrix' }, and returns an Array otherwise.
	   * Note that the type of the returned values is taken from the type of the
	   * provided start/end value. If only one of these is a built-in `number` type,
	   * it will be promoted to the type of the other endpoint. However, in the case
	   * of Unit values, both endpoints must have compatible units, and the return
	   * value will have compatible units as well.
	   *
	   * Examples:
	   *
	   *     math.range(2, 6)        // [2, 3, 4, 5]
	   *     math.range(2, -3, -1)   // [2, 1, 0, -1, -2]
	   *     math.range('2:1:6')     // [2, 3, 4, 5]
	   *     math.range(2, 6, true)  // [2, 3, 4, 5, 6]
	   *     math.range(2, math.fraction(8,3), math.fraction(1,3)) // [fraction(2), fraction(7,3)]
	   *     math.range(math.unit(2, 'm'), math.unit(-3, 'm'), math.unit(-1, 'm')) // [2 m, 1 m, 0 m , -1 m, -2 m]
	   *
	   * See also:
	   *
	   *     ones, zeros, size, subset
	   *
	   * @param {*} args   Parameters describing the range's `start`, `end`, and optional `step`.
	   * @return {Array | Matrix} range
	   */
	  return typed(name, {
	    // TODO: simplify signatures when typed-function supports default values and optional arguments

	    string: _strRange,
	    'string, boolean': _strRange,
	    number: function (oops) {
	      throw new TypeError(`Too few arguments to function range(): ${oops}`);
	    },
	    boolean: function (oops) {
	      throw new TypeError(`Unexpected type of argument 1 to function range(): ${oops}, number|bigint|BigNumber|Fraction`);
	    },
	    'number, number': function (start, end) {
	      return _out(_range(start, end, 1, false));
	    },
	    'number, number, number': function (start, end, step) {
	      return _out(_range(start, end, step, false));
	    },
	    'number, number, boolean': function (start, end, includeEnd) {
	      return _out(_range(start, end, 1, includeEnd));
	    },
	    'number, number, number, boolean': function (start, end, step, includeEnd) {
	      return _out(_range(start, end, step, includeEnd));
	    },
	    // Handle bigints; if either limit is bigint, range should be too
	    'bigint, bigint|number': function (start, end) {
	      return _out(_range(start, end, 1n, false));
	    },
	    'number, bigint': function (start, end) {
	      return _out(_range(BigInt(start), end, 1n, false));
	    },
	    'bigint, bigint|number, bigint|number': function (start, end, step) {
	      return _out(_range(start, end, BigInt(step), false));
	    },
	    'number, bigint, bigint|number': function (start, end, step) {
	      return _out(_range(BigInt(start), end, BigInt(step), false));
	    },
	    'bigint, bigint|number, boolean': function (start, end, includeEnd) {
	      return _out(_range(start, end, 1n, includeEnd));
	    },
	    'number, bigint, boolean': function (start, end, includeEnd) {
	      return _out(_range(BigInt(start), end, 1n, includeEnd));
	    },
	    'bigint, bigint|number, bigint|number, boolean': function (start, end, step, includeEnd) {
	      return _out(_range(start, end, BigInt(step), includeEnd));
	    },
	    'number, bigint, bigint|number, boolean': function (start, end, step, includeEnd) {
	      return _out(_range(BigInt(start), end, BigInt(step), includeEnd));
	    },
	    'BigNumber, BigNumber': function (start, end) {
	      const BigNumber = start.constructor;
	      return _out(_range(start, end, new BigNumber(1), false));
	    },
	    'BigNumber, BigNumber, BigNumber': function (start, end, step) {
	      return _out(_range(start, end, step, false));
	    },
	    'BigNumber, BigNumber, boolean': function (start, end, includeEnd) {
	      const BigNumber = start.constructor;
	      return _out(_range(start, end, new BigNumber(1), includeEnd));
	    },
	    'BigNumber, BigNumber, BigNumber, boolean': function (start, end, step, includeEnd) {
	      return _out(_range(start, end, step, includeEnd));
	    },
	    'Fraction, Fraction': function (start, end) {
	      return _out(_range(start, end, 1, false));
	    },
	    'Fraction, Fraction, Fraction': function (start, end, step) {
	      return _out(_range(start, end, step, false));
	    },
	    'Fraction, Fraction, boolean': function (start, end, includeEnd) {
	      return _out(_range(start, end, 1, includeEnd));
	    },
	    'Fraction, Fraction, Fraction, boolean': function (start, end, step, includeEnd) {
	      return _out(_range(start, end, step, includeEnd));
	    },
	    'Unit, Unit, Unit': function (start, end, step) {
	      return _out(_range(start, end, step, false));
	    },
	    'Unit, Unit, Unit, boolean': function (start, end, step, includeEnd) {
	      return _out(_range(start, end, step, includeEnd));
	    }
	  });
	  function _out(arr) {
	    if (config.matrix === 'Matrix') {
	      return matrix ? matrix(arr) : (0, _noop.noMatrix)();
	    }
	    return arr;
	  }
	  function _strRange(str, includeEnd) {
	    const r = _parse(str);
	    if (!r) {
	      throw new SyntaxError('String "' + str + '" is no valid range');
	    }
	    if (config.number === 'BigNumber') {
	      if (bignumber === undefined) {
	        (0, _noop.noBignumber)();
	      }
	      return _out(_range(bignumber(r.start), bignumber(r.end), bignumber(r.step)));
	    } else {
	      return _out(_range(r.start, r.end, r.step, includeEnd));
	    }
	  }

	  /**
	   * Create a range with numbers or BigNumbers
	   * @param {number | BigNumber | Unit} start
	   * @param {number | BigNumber | Unit} end
	   * @param {number | BigNumber | Unit} step
	   * @param {boolean} includeEnd
	   * @returns {Array} range
	   * @private
	   */
	  function _range(start, end, step, includeEnd) {
	    const array = [];
	    if (isZero(step)) throw new Error('Step must be non-zero');
	    const ongoing = isPositive(step) ? includeEnd ? smallerEq : smaller : includeEnd ? largerEq : larger;
	    let x = start;
	    while (ongoing(x, end)) {
	      array.push(x);
	      x = add(x, step);
	    }
	    return array;
	  }

	  /**
	   * Parse a string into a range,
	   * The string contains the start, optional step, and end, separated by a colon.
	   * If the string does not contain a valid range, null is returned.
	   * For example str='0:2:11'.
	   * @param {string} str
	   * @return {{start: number, end: number, step: number} | null} range Object containing properties start, end, step
	   * @private
	   */
	  function _parse(str) {
	    const args = str.split(':');

	    // number
	    const nums = args.map(function (arg) {
	      // use Number and not parseFloat as Number returns NaN on invalid garbage in the string
	      return Number(arg);
	    });
	    const invalid = nums.some(function (num) {
	      return isNaN(num);
	    });
	    if (invalid) {
	      return null;
	    }
	    switch (nums.length) {
	      case 2:
	        return {
	          start: nums[0],
	          end: nums[1],
	          step: 1
	        };
	      case 3:
	        return {
	          start: nums[0],
	          end: nums[2],
	          step: nums[1]
	        };
	      default:
	        return null;
	    }
	  }
	});
	return range$1;
}

var reshape$1 = {};

var hasRequiredReshape$1;

function requireReshape$1 () {
	if (hasRequiredReshape$1) return reshape$1;
	hasRequiredReshape$1 = 1;

	Object.defineProperty(reshape$1, "__esModule", {
	  value: true
	});
	reshape$1.createReshape = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'reshape';
	const dependencies = ['typed', 'isInteger', 'matrix'];
	reshape$1.createReshape = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isInteger
	  } = _ref;
	  /**
	   * Reshape a multi dimensional array to fit the specified dimensions
	   *
	   * Syntax:
	   *
	   *     math.reshape(x, sizes)
	   *
	   * Examples:
	   *
	   *     math.reshape([1, 2, 3, 4, 5, 6], [2, 3])
	   *     // returns Array  [[1, 2, 3], [4, 5, 6]]
	   *
	   *     math.reshape([[1, 2], [3, 4]], [1, 4])
	   *     // returns Array  [[1, 2, 3, 4]]
	   *
	   *     math.reshape([[1, 2], [3, 4]], [4])
	   *     // returns Array [1, 2, 3, 4]
	   *
	   *     const x = math.matrix([1, 2, 3, 4, 5, 6, 7, 8])
	   *     math.reshape(x, [2, 2, 2])
	   *     // returns Matrix [[[1, 2], [3, 4]], [[5, 6], [7, 8]]]
	   *
	   *    math.reshape([1, 2, 3, 4], [-1, 2])
	   *    // returns Matrix [[1, 2], [3, 4]]
	   *
	   * See also:
	   *
	   *     size, squeeze, resize
	   *
	   * @param {Array | Matrix | *} x  Matrix to be reshaped
	   * @param {number[]} sizes        One dimensional array with integral sizes for
	   *                                each dimension. One -1 is allowed as wildcard,
	   *                                which calculates this dimension automatically.
	   *
	   * @return {* | Array | Matrix}   A reshaped clone of matrix `x`
	   *
	   * @throws {TypeError}            If `sizes` does not contain solely integers
	   * @throws {DimensionError}       If the product of the new dimension sizes does
	   *                                not equal that of the old ones
	   */
	  return typed(name, {
	    'Matrix, Array': function (x, sizes) {
	      return x.reshape(sizes, true);
	    },
	    'Array, Array': function (x, sizes) {
	      sizes.forEach(function (size) {
	        if (!isInteger(size)) {
	          throw new TypeError('Invalid size for dimension: ' + size);
	        }
	      });
	      return (0, _array.reshape)(x, sizes);
	    }
	  });
	});
	return reshape$1;
}

var resize$1 = {};

var hasRequiredResize$1;

function requireResize$1 () {
	if (hasRequiredResize$1) return resize$1;
	hasRequiredResize$1 = 1;

	Object.defineProperty(resize$1, "__esModule", {
	  value: true
	});
	resize$1.createResize = void 0;
	var _is = requireIs();
	var _DimensionError = requireDimensionError();
	var _ArgumentsError = requireArgumentsError();
	var _number = requireNumber$3();
	var _string = requireString$2();
	var _object = requireObject$3();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'resize';
	const dependencies = ['config', 'matrix'];
	resize$1.createResize = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    config,
	    matrix
	  } = _ref;
	  /**
	   * Resize a matrix
	   *
	   * Syntax:
	   *
	   *     math.resize(x, size)
	   *     math.resize(x, size, defaultValue)
	   *
	   * Examples:
	   *
	   *     math.resize([1, 2, 3, 4, 5], [3]) // returns Array  [1, 2, 3]
	   *     math.resize([1, 2, 3], [5], 0)    // returns Array  [1, 2, 3, 0, 0]
	   *     math.resize(2, [2, 3], 0)         // returns Matrix [[2, 0, 0], [0, 0, 0]]
	   *     math.resize("hello", [8], "!")    // returns string 'hello!!!'
	   *
	   * See also:
	   *
	   *     size, squeeze, subset, reshape
	   *
	   * @param {Array | Matrix | *} x             Matrix to be resized
	   * @param {Array | Matrix} size              One dimensional array with numbers
	   * @param {number | string} [defaultValue=0] Zero by default, except in
	   *                                           case of a string, in that case
	   *                                           defaultValue = ' '
	   * @return {* | Array | Matrix} A resized clone of matrix `x`
	   */
	  // TODO: rework resize to a typed-function
	  return function resize(x, size, defaultValue) {
	    if (arguments.length !== 2 && arguments.length !== 3) {
	      throw new _ArgumentsError.ArgumentsError('resize', arguments.length, 2, 3);
	    }
	    if ((0, _is.isMatrix)(size)) {
	      size = size.valueOf(); // get Array
	    }
	    if ((0, _is.isBigNumber)(size[0])) {
	      // convert bignumbers to numbers
	      size = size.map(function (value) {
	        return !(0, _is.isBigNumber)(value) ? value : value.toNumber();
	      });
	    }

	    // check x is a Matrix
	    if ((0, _is.isMatrix)(x)) {
	      // use optimized matrix implementation, return copy
	      return x.resize(size, defaultValue, true);
	    }
	    if (typeof x === 'string') {
	      // resize string
	      return _resizeString(x, size, defaultValue);
	    }

	    // check result should be a matrix
	    const asMatrix = Array.isArray(x) ? false : config.matrix !== 'Array';
	    if (size.length === 0) {
	      // output a scalar
	      while (Array.isArray(x)) {
	        x = x[0];
	      }
	      return (0, _object.clone)(x);
	    } else {
	      // output an array/matrix
	      if (!Array.isArray(x)) {
	        x = [x];
	      }
	      x = (0, _object.clone)(x);
	      const res = (0, _array.resize)(x, size, defaultValue);
	      return asMatrix ? matrix(res) : res;
	    }
	  };

	  /**
	   * Resize a string
	   * @param {string} str
	   * @param {number[]} size
	   * @param {string} [defaultChar=' ']
	   * @private
	   */
	  function _resizeString(str, size, defaultChar) {
	    if (defaultChar !== undefined) {
	      if (typeof defaultChar !== 'string' || defaultChar.length !== 1) {
	        throw new TypeError('Single character expected as defaultValue');
	      }
	    } else {
	      defaultChar = ' ';
	    }
	    if (size.length !== 1) {
	      throw new _DimensionError.DimensionError(size.length, 1);
	    }
	    const len = size[0];
	    if (typeof len !== 'number' || !(0, _number.isInteger)(len)) {
	      throw new TypeError('Invalid size, must contain positive integers ' + '(size: ' + (0, _string.format)(size) + ')');
	    }
	    if (str.length > len) {
	      return str.substring(0, len);
	    } else if (str.length < len) {
	      let res = str;
	      for (let i = 0, ii = len - str.length; i < ii; i++) {
	        res += defaultChar;
	      }
	      return res;
	    } else {
	      return str;
	    }
	  }
	});
	return resize$1;
}

var rotate$1 = {};

var hasRequiredRotate$1;

function requireRotate$1 () {
	if (hasRequiredRotate$1) return rotate$1;
	hasRequiredRotate$1 = 1;

	Object.defineProperty(rotate$1, "__esModule", {
	  value: true
	});
	rotate$1.createRotate = void 0;
	var _factory = requireFactory$b();
	var _array = requireArray();
	const name = 'rotate';
	const dependencies = ['typed', 'multiply', 'rotationMatrix'];
	rotate$1.createRotate = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    multiply,
	    rotationMatrix
	  } = _ref;
	  /**
	     * Rotate a vector of size 1x2 counter-clockwise by a given angle
	     * Rotate a vector of size 1x3 counter-clockwise by a given angle around the given axis
	     *
	     * Syntax:
	     *
	     *    math.rotate(w, theta)
	     *    math.rotate(w, theta, v)
	     *
	     * Examples:
	     *
	     *    math.rotate([11, 12], math.pi / 2)                           // returns [-12, 11]
	     *    math.rotate(matrix([11, 12]), math.pi / 2)                   // returns [-12, 11]
	     *
	     *    math.rotate([1, 0, 0], unit('90deg'), [0, 0, 1])             // returns [0, 1, 0]
	     *    math.rotate(matrix([1, 0, 0]), unit('90deg'), [0, 0, 1])     // returns Matrix [0, 1, 0]
	     *
	     *    math.rotate([1, 0], math.complex(1 + i))                     // returns [cos(1 + i) - sin(1 + i), sin(1 + i) + cos(1 + i)]
	     *
	     * See also:
	     *
	     *    matrix, rotationMatrix
	     *
	     * @param {Array | Matrix} w                             Vector to rotate
	     * @param {number | BigNumber | Complex | Unit} theta    Rotation angle
	     * @param {Array | Matrix} [v]                           Rotation axis
	     * @return {Array | Matrix}                              Multiplication of the rotation matrix and w
	     */
	  return typed(name, {
	    'Array , number | BigNumber | Complex | Unit': function (w, theta) {
	      _validateSize(w, 2);
	      const matrixRes = multiply(rotationMatrix(theta), w);
	      return matrixRes.toArray();
	    },
	    'Matrix , number | BigNumber | Complex | Unit': function (w, theta) {
	      _validateSize(w, 2);
	      return multiply(rotationMatrix(theta), w);
	    },
	    'Array, number | BigNumber | Complex | Unit, Array | Matrix': function (w, theta, v) {
	      _validateSize(w, 3);
	      const matrixRes = multiply(rotationMatrix(theta, v), w);
	      return matrixRes;
	    },
	    'Matrix, number | BigNumber | Complex | Unit, Array | Matrix': function (w, theta, v) {
	      _validateSize(w, 3);
	      return multiply(rotationMatrix(theta, v), w);
	    }
	  });
	  function _validateSize(v, expectedSize) {
	    const actualSize = Array.isArray(v) ? (0, _array.arraySize)(v) : v.size();
	    if (actualSize.length > 2) {
	      throw new RangeError(`Vector must be of dimensions 1x${expectedSize}`);
	    }
	    if (actualSize.length === 2 && actualSize[1] !== 1) {
	      throw new RangeError(`Vector must be of dimensions 1x${expectedSize}`);
	    }
	    if (actualSize[0] !== expectedSize) {
	      throw new RangeError(`Vector must be of dimensions 1x${expectedSize}`);
	    }
	  }
	});
	return rotate$1;
}

var rotationMatrix$1 = {};

var hasRequiredRotationMatrix$1;

function requireRotationMatrix$1 () {
	if (hasRequiredRotationMatrix$1) return rotationMatrix$1;
	hasRequiredRotationMatrix$1 = 1;

	Object.defineProperty(rotationMatrix$1, "__esModule", {
	  value: true
	});
	rotationMatrix$1.createRotationMatrix = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'rotationMatrix';
	const dependencies = ['typed', 'config', 'multiplyScalar', 'addScalar', 'unaryMinus', 'norm', 'matrix', 'BigNumber', 'DenseMatrix', 'SparseMatrix', 'cos', 'sin'];
	rotationMatrix$1.createRotationMatrix = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    multiplyScalar,
	    addScalar,
	    unaryMinus,
	    norm,
	    BigNumber,
	    matrix,
	    DenseMatrix,
	    SparseMatrix,
	    cos,
	    sin
	  } = _ref;
	  /**
	   * Create a 2-dimensional counter-clockwise rotation matrix (2x2) for a given angle (expressed in radians).
	   * Create a 2-dimensional counter-clockwise rotation matrix (3x3) by a given angle (expressed in radians) around a given axis (1x3).
	   *
	   * Syntax:
	   *
	   *    math.rotationMatrix(theta)
	   *    math.rotationMatrix(theta, format)
	   *    math.rotationMatrix(theta, [v])
	   *    math.rotationMatrix(theta, [v], format)
	   *
	   * Examples:
	   *
	   *    math.rotationMatrix(math.pi / 2)                      // returns [[0, -1], [1, 0]]
	   *    math.rotationMatrix(math.bignumber(1))                // returns [[bignumber(cos(1)), bignumber(-sin(1))], [bignumber(sin(1)), bignumber(cos(1))]]
	   *    math.rotationMatrix(math.complex(1 + i))              // returns [[cos(1 + i), -sin(1 + i)], [sin(1 + i), cos(1 + i)]]
	   *    math.rotationMatrix(math.unit('1rad'))                // returns [[cos(1), -sin(1)], [sin(1), cos(1)]]
	   *
	   *    math.rotationMatrix(math.pi / 2, [0, 1, 0])           // returns [[0, 0, 1], [0, 1, 0], [-1, 0, 0]]
	   *    math.rotationMatrix(math.pi / 2, matrix([0, 1, 0]))   // returns matrix([[0, 0, 1], [0, 1, 0], [-1, 0, 0]])
	   *
	   *
	   * See also:
	   *
	   *    matrix, cos, sin
	   *
	   *
	   * @param {number | BigNumber | Complex | Unit} theta    Rotation angle
	   * @param {Array | Matrix} [v]                           Rotation axis
	   * @param {string} [format]                              Result Matrix storage format
	   * @return {Array | Matrix}                              Rotation matrix
	   */

	  return typed(name, {
	    '': function () {
	      return config.matrix === 'Matrix' ? matrix([]) : [];
	    },
	    string: function (format) {
	      return matrix(format);
	    },
	    'number | BigNumber | Complex | Unit': function (theta) {
	      return _rotationMatrix2x2(theta, config.matrix === 'Matrix' ? 'dense' : undefined);
	    },
	    'number | BigNumber | Complex | Unit, string': function (theta, format) {
	      return _rotationMatrix2x2(theta, format);
	    },
	    'number | BigNumber | Complex | Unit, Array': function (theta, v) {
	      const matrixV = matrix(v);
	      _validateVector(matrixV);
	      return _rotationMatrix3x3(theta, matrixV, undefined);
	    },
	    'number | BigNumber | Complex | Unit, Matrix': function (theta, v) {
	      _validateVector(v);
	      const storageType = v.storage() || (config.matrix === 'Matrix' ? 'dense' : undefined);
	      return _rotationMatrix3x3(theta, v, storageType);
	    },
	    'number | BigNumber | Complex | Unit, Array, string': function (theta, v, format) {
	      const matrixV = matrix(v);
	      _validateVector(matrixV);
	      return _rotationMatrix3x3(theta, matrixV, format);
	    },
	    'number | BigNumber | Complex | Unit, Matrix, string': function (theta, v, format) {
	      _validateVector(v);
	      return _rotationMatrix3x3(theta, v, format);
	    }
	  });

	  /**
	   * Returns 2x2 matrix of 2D rotation of angle theta
	   *
	   * @param {number | BigNumber | Complex | Unit} theta  The rotation angle
	   * @param {string} format                              The result Matrix storage format
	   * @returns {Matrix}
	   * @private
	   */
	  function _rotationMatrix2x2(theta, format) {
	    const Big = (0, _is.isBigNumber)(theta);
	    const minusOne = Big ? new BigNumber(-1) : -1;
	    const cosTheta = cos(theta);
	    const sinTheta = sin(theta);
	    const data = [[cosTheta, multiplyScalar(minusOne, sinTheta)], [sinTheta, cosTheta]];
	    return _convertToFormat(data, format);
	  }
	  function _validateVector(v) {
	    const size = v.size();
	    if (size.length < 1 || size[0] !== 3) {
	      throw new RangeError('Vector must be of dimensions 1x3');
	    }
	  }
	  function _mul(array) {
	    return array.reduce((p, curr) => multiplyScalar(p, curr));
	  }
	  function _convertToFormat(data, format) {
	    if (format) {
	      if (format === 'sparse') {
	        return new SparseMatrix(data);
	      }
	      if (format === 'dense') {
	        return new DenseMatrix(data);
	      }
	      throw new TypeError(`Unknown matrix type "${format}"`);
	    }
	    return data;
	  }

	  /**
	   * Returns a 3x3 matrix of rotation of angle theta around vector v
	   *
	   * @param {number | BigNumber | Complex | Unit} theta The rotation angle
	   * @param {Matrix} v                                  The rotation axis vector
	   * @param {string} format                             The storage format of the resulting matrix
	   * @returns {Matrix}
	   * @private
	   */
	  function _rotationMatrix3x3(theta, v, format) {
	    const normV = norm(v);
	    if (normV === 0) {
	      throw new RangeError('Rotation around zero vector');
	    }
	    const Big = (0, _is.isBigNumber)(theta) ? BigNumber : null;
	    const one = Big ? new Big(1) : 1;
	    const minusOne = Big ? new Big(-1) : -1;
	    const vx = Big ? new Big(v.get([0]) / normV) : v.get([0]) / normV;
	    const vy = Big ? new Big(v.get([1]) / normV) : v.get([1]) / normV;
	    const vz = Big ? new Big(v.get([2]) / normV) : v.get([2]) / normV;
	    const c = cos(theta);
	    const oneMinusC = addScalar(one, unaryMinus(c));
	    const s = sin(theta);
	    const r11 = addScalar(c, _mul([vx, vx, oneMinusC]));
	    const r12 = addScalar(_mul([vx, vy, oneMinusC]), _mul([minusOne, vz, s]));
	    const r13 = addScalar(_mul([vx, vz, oneMinusC]), _mul([vy, s]));
	    const r21 = addScalar(_mul([vx, vy, oneMinusC]), _mul([vz, s]));
	    const r22 = addScalar(c, _mul([vy, vy, oneMinusC]));
	    const r23 = addScalar(_mul([vy, vz, oneMinusC]), _mul([minusOne, vx, s]));
	    const r31 = addScalar(_mul([vx, vz, oneMinusC]), _mul([minusOne, vy, s]));
	    const r32 = addScalar(_mul([vy, vz, oneMinusC]), _mul([vx, s]));
	    const r33 = addScalar(c, _mul([vz, vz, oneMinusC]));
	    const data = [[r11, r12, r13], [r21, r22, r23], [r31, r32, r33]];
	    return _convertToFormat(data, format);
	  }
	});
	return rotationMatrix$1;
}

var row$1 = {};

var hasRequiredRow$1;

function requireRow$1 () {
	if (hasRequiredRow$1) return row$1;
	hasRequiredRow$1 = 1;

	Object.defineProperty(row$1, "__esModule", {
	  value: true
	});
	row$1.createRow = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _object = requireObject$3();
	var _array = requireArray();
	const name = 'row';
	const dependencies = ['typed', 'Index', 'matrix', 'range'];
	row$1.createRow = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Index,
	    matrix,
	    range
	  } = _ref;
	  /**
	   * Return a row from a Matrix.
	   *
	   * Syntax:
	   *
	   *     math.row(value, index)
	   *
	   * Example:
	   *
	   *     // get a row
	   *     const d = [[1, 2], [3, 4]]
	   *     math.row(d, 1) // returns [[3, 4]]
	   *
	   * See also:
	   *
	   *     column
	   *
	   * @param {Array | Matrix } value   An array or matrix
	   * @param {number} row              The index of the row
	   * @return {Array | Matrix}         The retrieved row
	   */
	  return typed(name, {
	    'Matrix, number': _row,
	    'Array, number': function (value, row) {
	      return _row(matrix((0, _object.clone)(value)), row).valueOf();
	    }
	  });

	  /**
	   * Retrieve a row of a matrix
	   * @param {Matrix } value  A matrix
	   * @param {number} row     The index of the row
	   * @return {Matrix}        The retrieved row
	   */
	  function _row(value, row) {
	    // check dimensions
	    if (value.size().length !== 2) {
	      throw new Error('Only two dimensional matrix is supported');
	    }
	    (0, _array.validateIndex)(row, value.size()[0]);
	    const columnRange = range(0, value.size()[1]);
	    const index = new Index([row], columnRange);
	    const result = value.subset(index);
	    // once config.legacySubset just return result
	    return (0, _is.isMatrix)(result) ? result : matrix([[result]]);
	  }
	});
	return row$1;
}

var size$1 = {};

var hasRequiredSize$1;

function requireSize$1 () {
	if (hasRequiredSize$1) return size$1;
	hasRequiredSize$1 = 1;

	Object.defineProperty(size$1, "__esModule", {
	  value: true
	});
	size$1.createSize = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'size';
	const dependencies = ['typed'];
	size$1.createSize = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the size of a matrix or scalar. Always returns an Array containing numbers.
	   *
	   * Note that in mathjs v14 and older, function size could return a Matrix depending on
	   * the input type and configuration.
	   *
	   * Syntax:
	   *
	   *     math.size(x)
	   *
	   * Examples:
	   *
	   *     math.size(2.3)                       // returns []
	   *     math.size('hello world')             // returns [11]
	   *
	   *     const A = [[1, 2, 3], [4, 5, 6]]
	   *     math.size(A)                         // returns [2, 3]
	   *     math.size(math.range(1,6).toArray()) // returns [5]
	   *
	   * See also:
	   *
	   *     count, resize, squeeze, subset
	   *
	   * @param {boolean | number | Complex | Unit | string | Array | Matrix} x  A matrix
	   * @return {Array} A vector with size of `x`.
	   */
	  return typed(name, {
	    Matrix: x => x.size(),
	    Array: _array.arraySize,
	    string: x => [x.length],
	    // scalar
	    'number | Complex | BigNumber | Unit | boolean | null': _x => []
	  });
	});
	return size$1;
}

var squeeze$1 = {};

var hasRequiredSqueeze$1;

function requireSqueeze$1 () {
	if (hasRequiredSqueeze$1) return squeeze$1;
	hasRequiredSqueeze$1 = 1;

	Object.defineProperty(squeeze$1, "__esModule", {
	  value: true
	});
	squeeze$1.createSqueeze = void 0;
	var _object = requireObject$3();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'squeeze';
	const dependencies = ['typed'];
	squeeze$1.createSqueeze = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Squeeze a matrix, remove inner and outer singleton dimensions from a matrix.
	   *
	   * Syntax:
	   *
	   *     math.squeeze(x)
	   *
	   * Examples:
	   *
	   *     math.squeeze([3])           // returns 3
	   *     math.squeeze([[3]])         // returns 3
	   *
	   *     const A = math.zeros(3, 1)    // returns [[0], [0], [0]] (size 3x1)
	   *     math.squeeze(A)             // returns [0, 0, 0] (size 3)
	   *
	   *     const B = math.zeros(1, 3)    // returns [[0, 0, 0]] (size 1x3)
	   *     math.squeeze(B)             // returns [0, 0, 0] (size 3)
	   *
	   *     // only inner and outer dimensions are removed
	   *     const C = math.zeros(2, 1, 3) // returns [[[0, 0, 0]], [[0, 0, 0]]] (size 2x1x3)
	   *     math.squeeze(C)             // returns [[[0, 0, 0]], [[0, 0, 0]]] (size 2x1x3)
	   *
	   * See also:
	   *
	   *     subset
	   *
	   * @param {Matrix | Array} x      Matrix to be squeezed
	   * @return {Matrix | Array} Squeezed matrix
	   */
	  return typed(name, {
	    Array: function (x) {
	      return (0, _array.squeeze)((0, _object.clone)(x));
	    },
	    Matrix: function (x) {
	      const res = (0, _array.squeeze)(x.toArray());
	      // FIXME: return the same type of matrix as the input
	      return Array.isArray(res) ? x.create(res, x.datatype()) : res;
	    },
	    any: function (x) {
	      // scalar
	      return (0, _object.clone)(x);
	    }
	  });
	});
	return squeeze$1;
}

var subset$1 = {};

var hasRequiredSubset$1;

function requireSubset$1 () {
	if (hasRequiredSubset$1) return subset$1;
	hasRequiredSubset$1 = 1;

	Object.defineProperty(subset$1, "__esModule", {
	  value: true
	});
	subset$1.createSubset = void 0;
	var _is = requireIs();
	var _object = requireObject$3();
	var _array = requireArray();
	var _customs = requireCustoms();
	var _DimensionError = requireDimensionError();
	var _factory = requireFactory$b();
	const name = 'subset';
	const dependencies = ['typed', 'matrix', 'zeros', 'add'];
	subset$1.createSubset = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    zeros,
	    add
	  } = _ref;
	  /**
	   * Get or set a subset of a matrix or string.
	   *
	   * Syntax:
	   *     math.subset(value, index)                                // retrieve a subset
	   *     math.subset(value, index, replacement [, defaultValue])  // replace a subset
	   *
	   * Examples:
	   *
	   *     // get a subset
	   *     const d = [[1, 2], [3, 4]]
	   *     math.subset(d, math.index(1, 0))             // returns 3
	   *     math.subset(d, math.index([0, 1], [1]))        // returns [[2], [4]]
	   *     math.subset(d, math.index([false, true], [0])) // returns [[3]]
	   *
	   *     // replace a subset
	   *     const e = []
	   *     const f = math.subset(e, math.index(0, [0, 2]), [5, 6])  // f = [[5, 0, 6]]
	   *     const g = math.subset(f, math.index(1, 1), 7, 0)         // g = [[5, 0, 6], [0, 7, 0]]
	   *     math.subset(g, math.index([false, true], 1), 8)          // returns [[5, 0, 6], [0, 8, 0]]
	   *
	   *     // get submatrix using ranges
	   *     const M = [
	   *       [1, 2, 3],
	   *       [4, 5, 6],
	   *       [7, 8, 9]
	   *     ]
	   *     math.subset(M, math.index(math.range(0,2), math.range(0,3))) // [[1, 2, 3], [4, 5, 6]]
	   *
	   * See also:
	   *
	   *     size, resize, squeeze, index
	   *
	   * @param {Array | Matrix | string} matrix  An array, matrix, or string
	   * @param {Index} index
	   *    For each dimension of the target, specifies an index or a list of
	   *    indices to fetch or set. `subset` uses the cartesian product of
	   *    the indices specified in each dimension.
	   * @param {*} [replacement]                 An array, matrix, or scalar.
	   *                                          If provided, the subset is replaced with replacement.
	   *                                          If not provided, the subset is returned
	   * @param {*} [defaultValue=undefined]      Default value, filled in on new entries when
	   *                                          the matrix is resized. If not provided,
	   *                                          math.matrix elements will be left undefined.
	   * @return {Array | Matrix | string} Either the retrieved subset or the updated matrix.
	   */

	  return typed(name, {
	    // get subset
	    'Matrix, Index': function (value, index) {
	      if ((0, _array.isEmptyIndex)(index)) {
	        return matrix();
	      }
	      (0, _array.validateIndexSourceSize)(value, index);
	      return value.subset(index);
	    },
	    'Array, Index': typed.referTo('Matrix, Index', function (subsetRef) {
	      return function (value, index) {
	        const subsetResult = subsetRef(matrix(value), index);
	        return index.isScalar() ? subsetResult : subsetResult.valueOf();
	      };
	    }),
	    'Object, Index': _getObjectProperty,
	    'string, Index': _getSubstring,
	    // set subset
	    'Matrix, Index, any, any': function (value, index, replacement, defaultValue) {
	      if ((0, _array.isEmptyIndex)(index)) {
	        return value;
	      }
	      (0, _array.validateIndexSourceSize)(value, index);
	      return value.clone().subset(index, _broadcastReplacement(replacement, index), defaultValue);
	    },
	    'Array, Index, any, any': typed.referTo('Matrix, Index, any, any', function (subsetRef) {
	      return function (value, index, replacement, defaultValue) {
	        const subsetResult = subsetRef(matrix(value), index, replacement, defaultValue);
	        return subsetResult.isMatrix ? subsetResult.valueOf() : subsetResult;
	      };
	    }),
	    'Array, Index, any': typed.referTo('Matrix, Index, any, any', function (subsetRef) {
	      return function (value, index, replacement) {
	        return subsetRef(matrix(value), index, replacement, undefined).valueOf();
	      };
	    }),
	    'Matrix, Index, any': typed.referTo('Matrix, Index, any, any', function (subsetRef) {
	      return function (value, index, replacement) {
	        return subsetRef(value, index, replacement, undefined);
	      };
	    }),
	    'string, Index, string': _setSubstring,
	    'string, Index, string, string': _setSubstring,
	    'Object, Index, any': _setObjectProperty
	  });

	  /**
	   * Broadcasts a replacment value to be the same size as index
	   * @param {number | BigNumber | Array | Matrix} replacement Replacement value to try to broadcast
	   * @param {*} index Index value
	   * @returns broadcasted replacement that matches the size of index
	   */

	  function _broadcastReplacement(replacement, index) {
	    if (typeof replacement === 'string') {
	      throw new Error('can\'t boradcast a string');
	    }
	    if (index.isScalar()) {
	      return replacement;
	    }
	    const indexSize = index.size();
	    if (indexSize.every(d => d > 0)) {
	      try {
	        return add(replacement, zeros(indexSize));
	      } catch (error) {
	        return replacement;
	      }
	    } else {
	      return replacement;
	    }
	  }
	});

	/**
	 * Retrieve a subset of a string
	 * @param {string} str            string from which to get a substring
	 * @param {Index} index           An index or list of indices (character positions)
	 * @returns {string} substring
	 * @private
	 */
	function _getSubstring(str, index) {
	  if (!(0, _is.isIndex)(index)) {
	    // TODO: better error message
	    throw new TypeError('Index expected');
	  }
	  if ((0, _array.isEmptyIndex)(index)) {
	    return '';
	  }
	  (0, _array.validateIndexSourceSize)(Array.from(str), index);
	  if (index.size().length !== 1) {
	    throw new _DimensionError.DimensionError(index.size().length, 1);
	  }

	  // validate whether the range is out of range
	  const strLen = str.length;
	  (0, _array.validateIndex)(index.min()[0], strLen);
	  (0, _array.validateIndex)(index.max()[0], strLen);
	  const range = index.dimension(0);
	  let substr = '';
	  function callback(v) {
	    substr += str.charAt(v);
	  }
	  if (Number.isInteger(range)) {
	    callback(range);
	  } else {
	    range.forEach(callback);
	  }
	  return substr;
	}

	/**
	 * Replace a substring in a string
	 * @param {string} str            string to be replaced
	 * @param {Index} index           An index or list of indices (character positions)
	 * @param {string} replacement    Replacement string
	 * @param {string} [defaultValue] Default value to be used when resizing
	 *                                the string. is ' ' by default
	 * @returns {string} result
	 * @private
	 */
	function _setSubstring(str, index, replacement, defaultValue) {
	  if (!index || index.isIndex !== true) {
	    // TODO: better error message
	    throw new TypeError('Index expected');
	  }
	  if ((0, _array.isEmptyIndex)(index)) {
	    return str;
	  }
	  (0, _array.validateIndexSourceSize)(Array.from(str), index);
	  if (index.size().length !== 1) {
	    throw new _DimensionError.DimensionError(index.size().length, 1);
	  }
	  if (defaultValue !== undefined) {
	    if (typeof defaultValue !== 'string' || defaultValue.length !== 1) {
	      throw new TypeError('Single character expected as defaultValue');
	    }
	  } else {
	    defaultValue = ' ';
	  }
	  const range = index.dimension(0);
	  const len = Number.isInteger(range) ? 1 : range.size()[0];
	  if (len !== replacement.length) {
	    throw new _DimensionError.DimensionError(range.size()[0], replacement.length);
	  }

	  // validate whether the range is out of range
	  const strLen = str.length;
	  (0, _array.validateIndex)(index.min()[0]);
	  (0, _array.validateIndex)(index.max()[0]);

	  // copy the string into an array with characters
	  const chars = [];
	  for (let i = 0; i < strLen; i++) {
	    chars[i] = str.charAt(i);
	  }
	  function callback(v, i) {
	    chars[v] = replacement.charAt(i[0]);
	  }
	  if (Number.isInteger(range)) {
	    callback(range, [0]);
	  } else {
	    range.forEach(callback);
	  }

	  // initialize undefined characters with a space
	  if (chars.length > strLen) {
	    for (let i = strLen - 1, len = chars.length; i < len; i++) {
	      if (!chars[i]) {
	        chars[i] = defaultValue;
	      }
	    }
	  }
	  return chars.join('');
	}

	/**
	 * Retrieve a property from an object
	 * @param {Object} object
	 * @param {Index} index
	 * @return {*} Returns the value of the property
	 * @private
	 */
	function _getObjectProperty(object, index) {
	  if ((0, _array.isEmptyIndex)(index)) {
	    return undefined;
	  }
	  if (index.size().length !== 1) {
	    throw new _DimensionError.DimensionError(index.size(), 1);
	  }
	  const key = index.dimension(0);
	  if (typeof key !== 'string') {
	    throw new TypeError('String expected as index to retrieve an object property');
	  }
	  return (0, _customs.getSafeProperty)(object, key);
	}

	/**
	 * Set a property on an object
	 * @param {Object} object
	 * @param {Index} index
	 * @param {*} replacement
	 * @return {*} Returns the updated object
	 * @private
	 */
	function _setObjectProperty(object, index, replacement) {
	  if ((0, _array.isEmptyIndex)(index)) {
	    return object;
	  }
	  if (index.size().length !== 1) {
	    throw new _DimensionError.DimensionError(index.size(), 1);
	  }
	  const key = index.dimension(0);
	  if (typeof key !== 'string') {
	    throw new TypeError('String expected as index to retrieve an object property');
	  }

	  // clone the object, and apply the property to the clone
	  const updated = (0, _object.clone)(object);
	  (0, _customs.setSafeProperty)(updated, key, replacement);
	  return updated;
	}
	return subset$1;
}

var transpose$1 = {};

var hasRequiredTranspose$1;

function requireTranspose$1 () {
	if (hasRequiredTranspose$1) return transpose$1;
	hasRequiredTranspose$1 = 1;

	Object.defineProperty(transpose$1, "__esModule", {
	  value: true
	});
	transpose$1.createTranspose = void 0;
	var _object = requireObject$3();
	var _string = requireString$2();
	var _factory = requireFactory$b();
	const name = 'transpose';
	const dependencies = ['typed', 'matrix'];
	transpose$1.createTranspose = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix
	  } = _ref;
	  /**
	   * Transpose a matrix. All values of the matrix are reflected over its
	   * main diagonal. Only applicable to two dimensional matrices containing
	   * a vector (i.e. having size `[1,n]` or `[n,1]`). One dimensional
	   * vectors and scalars return the input unchanged.
	   *
	   * Syntax:
	   *
	   *     math.transpose(x)
	   *
	   * Examples:
	   *
	   *     const A = [[1, 2, 3], [4, 5, 6]]
	   *     math.transpose(A)               // returns [[1, 4], [2, 5], [3, 6]]
	   *
	   * See also:
	   *
	   *     diag, inv, subset, squeeze
	   *
	   * @param {Array | Matrix} x  Matrix to be transposed
	   * @return {Array | Matrix}   The transposed matrix
	   */
	  return typed(name, {
	    Array: x => transposeMatrix(matrix(x)).valueOf(),
	    Matrix: transposeMatrix,
	    any: _object.clone // scalars
	  });
	  function transposeMatrix(x) {
	    // matrix size
	    const size = x.size();

	    // result
	    let c;

	    // process dimensions
	    switch (size.length) {
	      case 1:
	        // vector
	        c = x.clone();
	        break;
	      case 2:
	        {
	          // rows and columns
	          const rows = size[0];
	          const columns = size[1];

	          // check columns
	          if (columns === 0) {
	            // throw exception
	            throw new RangeError('Cannot transpose a 2D matrix with no columns (size: ' + (0, _string.format)(size) + ')');
	          }

	          // process storage format
	          switch (x.storage()) {
	            case 'dense':
	              c = _denseTranspose(x, rows, columns);
	              break;
	            case 'sparse':
	              c = _sparseTranspose(x, rows, columns);
	              break;
	          }
	        }
	        break;
	      default:
	        // multi dimensional
	        throw new RangeError('Matrix must be a vector or two dimensional (size: ' + (0, _string.format)(size) + ')');
	    }
	    return c;
	  }
	  function _denseTranspose(m, rows, columns) {
	    // matrix array
	    const data = m._data;
	    // transposed matrix data
	    const transposed = [];
	    let transposedRow;
	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // initialize row
	      transposedRow = transposed[j] = [];
	      // loop rows
	      for (let i = 0; i < rows; i++) {
	        // set data
	        transposedRow[i] = (0, _object.clone)(data[i][j]);
	      }
	    }
	    // return matrix
	    return m.createDenseMatrix({
	      data: transposed,
	      size: [columns, rows],
	      datatype: m._datatype
	    });
	  }
	  function _sparseTranspose(m, rows, columns) {
	    // matrix arrays
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;
	    // result matrices
	    const cvalues = values ? [] : undefined;
	    const cindex = [];
	    const cptr = [];
	    // row counts
	    const w = [];
	    for (let x = 0; x < rows; x++) {
	      w[x] = 0;
	    }
	    // vars
	    let p, l, j;
	    // loop values in matrix
	    for (p = 0, l = index.length; p < l; p++) {
	      // number of values in row
	      w[index[p]]++;
	    }
	    // cumulative sum
	    let sum = 0;
	    // initialize cptr with the cummulative sum of row counts
	    for (let i = 0; i < rows; i++) {
	      // update cptr
	      cptr.push(sum);
	      // update sum
	      sum += w[i];
	      // update w
	      w[i] = cptr[i];
	    }
	    // update cptr
	    cptr.push(sum);
	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // values & index in column
	      for (let k0 = ptr[j], k1 = ptr[j + 1], k = k0; k < k1; k++) {
	        // C values & index
	        const q = w[index[k]]++;
	        // C[j, i] = A[i, j]
	        cindex[q] = j;
	        // check we need to process values (pattern matrix)
	        if (values) {
	          cvalues[q] = (0, _object.clone)(values[k]);
	        }
	      }
	    }
	    // return matrix
	    return m.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [columns, rows],
	      datatype: m._datatype
	    });
	  }
	});
	return transpose$1;
}

var ctranspose$1 = {};

var hasRequiredCtranspose$1;

function requireCtranspose$1 () {
	if (hasRequiredCtranspose$1) return ctranspose$1;
	hasRequiredCtranspose$1 = 1;

	Object.defineProperty(ctranspose$1, "__esModule", {
	  value: true
	});
	ctranspose$1.createCtranspose = void 0;
	var _factory = requireFactory$b();
	const name = 'ctranspose';
	const dependencies = ['typed', 'transpose', 'conj'];
	ctranspose$1.createCtranspose = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    transpose,
	    conj
	  } = _ref;
	  /**
	   * Transpose and complex conjugate a matrix. All values of the matrix are
	   * reflected over its main diagonal and then the complex conjugate is
	   * taken. This is equivalent to complex conjugation for scalars and
	   * vectors.
	   *
	   * Syntax:
	   *
	   *     math.ctranspose(x)
	   *
	   * Examples:
	   *
	   *     const A = [[1, 2, 3], [4, 5, math.complex(6,7)]]
	   *     math.ctranspose(A)      // returns [[1, 4], [2, 5], [3, {re:6,im:-7}]]
	   *
	   * See also:
	   *
	   *     transpose, diag, inv, subset, squeeze
	   *
	   * @param {Array | Matrix} x  Matrix to be ctransposed
	   * @return {Array | Matrix}   The ctransposed matrix
	   */
	  return typed(name, {
	    any: function (x) {
	      return conj(transpose(x));
	    }
	  });
	});
	return ctranspose$1;
}

var zeros$1 = {};

var hasRequiredZeros$1;

function requireZeros$1 () {
	if (hasRequiredZeros$1) return zeros$1;
	hasRequiredZeros$1 = 1;

	Object.defineProperty(zeros$1, "__esModule", {
	  value: true
	});
	zeros$1.createZeros = void 0;
	var _is = requireIs();
	var _number = requireNumber$3();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'zeros';
	const dependencies = ['typed', 'config', 'matrix', 'BigNumber'];
	zeros$1.createZeros = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    BigNumber
	  } = _ref;
	  /**
	   * Create a matrix filled with zeros. The created matrix can have one or
	   * multiple dimensions.
	   *
	   * Syntax:
	   *
	   *    math.zeros(m)
	   *    math.zeros(m, format)
	   *    math.zeros(m, n)
	   *    math.zeros(m, n, format)
	   *    math.zeros([m, n])
	   *    math.zeros([m, n], format)
	   *
	   * Examples:
	   *
	   *    math.zeros()                   // returns []
	   *    math.zeros(3)                  // returns [0, 0, 0]
	   *    math.zeros(3, 2)               // returns [[0, 0], [0, 0], [0, 0]]
	   *    math.zeros(3, 'dense')         // returns [0, 0, 0]
	   *
	   *    const A = [[1, 2, 3], [4, 5, 6]]
	   *    math.zeros(math.size(A))       // returns [[0, 0, 0], [0, 0, 0]]
	   *
	   * See also:
	   *
	   *    ones, identity, size, range
	   *
	   * @param {...(number|BigNumber) | Array} size    The size of each dimension of the matrix
	   * @param {string} [format]           The Matrix storage format
	   *
	   * @return {Array | Matrix}           A matrix filled with zeros
	   */
	  return typed(name, {
	    '': function () {
	      return config.matrix === 'Array' ? _zeros([]) : _zeros([], 'default');
	    },
	    // math.zeros(m, n, p, ..., format)
	    // TODO: more accurate signature '...number | BigNumber, string' as soon as typed-function supports this
	    '...number | BigNumber | string': function (size) {
	      const last = size[size.length - 1];
	      if (typeof last === 'string') {
	        const format = size.pop();
	        return _zeros(size, format);
	      } else if (config.matrix === 'Array') {
	        return _zeros(size);
	      } else {
	        return _zeros(size, 'default');
	      }
	    },
	    Array: _zeros,
	    Matrix: function (size) {
	      const format = size.storage();
	      return _zeros(size.valueOf(), format);
	    },
	    'Array | Matrix, string': function (size, format) {
	      return _zeros(size.valueOf(), format);
	    }
	  });

	  /**
	   * Create an Array or Matrix with zeros
	   * @param {Array} size
	   * @param {string} [format='default']
	   * @return {Array | Matrix}
	   * @private
	   */
	  function _zeros(size, format) {
	    const hasBigNumbers = _normalize(size);
	    const defaultValue = hasBigNumbers ? new BigNumber(0) : 0;
	    _validate(size);
	    if (format) {
	      // return a matrix
	      const m = matrix(format);
	      if (size.length > 0) {
	        return m.resize(size, defaultValue);
	      }
	      return m;
	    } else {
	      // return an Array
	      const arr = [];
	      if (size.length > 0) {
	        return (0, _array.resize)(arr, size, defaultValue);
	      }
	      return arr;
	    }
	  }

	  // replace BigNumbers with numbers, returns true if size contained BigNumbers
	  function _normalize(size) {
	    let hasBigNumbers = false;
	    size.forEach(function (value, index, arr) {
	      if ((0, _is.isBigNumber)(value)) {
	        hasBigNumbers = true;
	        arr[index] = value.toNumber();
	      }
	    });
	    return hasBigNumbers;
	  }

	  // validate arguments
	  function _validate(size) {
	    size.forEach(function (value) {
	      if (typeof value !== 'number' || !(0, _number.isInteger)(value) || value < 0) {
	        throw new Error('Parameters in function zeros must be positive integers');
	      }
	    });
	  }
	});

	// TODO: zeros contains almost the same code as ones. Reuse this?
	return zeros$1;
}

var fft$1 = {};

var hasRequiredFft$1;

function requireFft$1 () {
	if (hasRequiredFft$1) return fft$1;
	hasRequiredFft$1 = 1;

	Object.defineProperty(fft$1, "__esModule", {
	  value: true
	});
	fft$1.createFft = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'fft';
	const dependencies = ['typed', 'matrix', 'addScalar', 'multiplyScalar', 'divideScalar', 'exp', 'tau', 'i', 'dotDivide', 'conj', 'pow', 'ceil', 'log2'];
	fft$1.createFft = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    addScalar,
	    multiplyScalar,
	    divideScalar,
	    exp,
	    tau,
	    i: I,
	    dotDivide,
	    conj,
	    pow,
	    ceil,
	    log2
	  } = _ref;
	  /**
	   * Calculate N-dimensional Fourier transform
	   *
	   * Syntax:
	   *
	   *     math.fft(arr)
	   *
	   * Examples:
	   *
	   *    math.fft([[1, 0], [1, 0]]) // returns [[{re:2, im:0}, {re:2, im:0}], [{re:0, im:0}, {re:0, im:0}]]
	   *
	   *
	   * See Also:
	   *
	   *      ifft
	   *
	   * @param {Array | Matrix} arr    An array or matrix
	   * @return {Array | Matrix}       N-dimensional Fourier transformation of the array
	   */
	  return typed(name, {
	    Array: _ndFft,
	    Matrix: function (matrix) {
	      return matrix.create(_ndFft(matrix.valueOf()), matrix.datatype());
	    }
	  });

	  /**
	   * Perform an N-dimensional Fourier transform
	   *
	   * @param {Array} arr      The array
	   * @return {Array}         resulting array
	   */
	  function _ndFft(arr) {
	    const size = (0, _array.arraySize)(arr);
	    if (size.length === 1) return _fft(arr, size[0]);
	    // ndFft along dimension 1,...,N-1 then 1dFft along dimension 0
	    return _1dFft(arr.map(slice => _ndFft(slice, size.slice(1))), 0);
	  }

	  /**
	   * Perform an 1-dimensional Fourier transform
	   *
	   * @param {Array} arr      The array
	   * @param {number} dim     dimension of the array to perform on
	   * @return {Array}         resulting array
	   */
	  function _1dFft(arr, dim) {
	    const size = (0, _array.arraySize)(arr);
	    if (dim !== 0) return new Array(size[0]).fill(0).map((_, i) => _1dFft(arr[i], dim - 1));
	    if (size.length === 1) return _fft(arr);
	    function _transpose(arr) {
	      // Swap first 2 dimensions
	      const size = (0, _array.arraySize)(arr);
	      return new Array(size[1]).fill(0).map((_, j) => new Array(size[0]).fill(0).map((_, i) => arr[i][j]));
	    }
	    return _transpose(_1dFft(_transpose(arr), 1));
	  }
	  /**
	   * Perform an 1-dimensional non-power-of-2 Fourier transform using Chirp-Z Transform
	   *
	   * @param {Array} arr      The array
	   * @return {Array}         resulting array
	   */
	  function _czt(arr) {
	    const n = arr.length;
	    const w = exp(divideScalar(multiplyScalar(-1, multiplyScalar(I, tau)), n));
	    const chirp = [];
	    for (let i = 1 - n; i < n; i++) {
	      chirp.push(pow(w, divideScalar(pow(i, 2), 2)));
	    }
	    const N2 = pow(2, ceil(log2(n + n - 1)));
	    const xp = [...new Array(n).fill(0).map((_, i) => multiplyScalar(arr[i], chirp[n - 1 + i])), ...new Array(N2 - n).fill(0)];
	    const ichirp = [...new Array(n + n - 1).fill(0).map((_, i) => divideScalar(1, chirp[i])), ...new Array(N2 - (n + n - 1)).fill(0)];
	    const fftXp = _fft(xp);
	    const fftIchirp = _fft(ichirp);
	    const fftProduct = new Array(N2).fill(0).map((_, i) => multiplyScalar(fftXp[i], fftIchirp[i]));
	    const ifftProduct = dotDivide(conj(_ndFft(conj(fftProduct))), N2);
	    const ret = [];
	    for (let i = n - 1; i < n + n - 1; i++) {
	      ret.push(multiplyScalar(ifftProduct[i], chirp[i]));
	    }
	    return ret;
	  }
	  /**
	   * Perform an 1-dimensional Fourier transform
	   *
	   * @param {Array} arr      The array
	   * @return {Array}         resulting array
	   */
	  function _fft(arr) {
	    const len = arr.length;
	    if (len === 1) return [arr[0]];
	    if (len % 2 === 0) {
	      const ret = [..._fft(arr.filter((_, i) => i % 2 === 0)), ..._fft(arr.filter((_, i) => i % 2 === 1))];
	      for (let k = 0; k < len / 2; k++) {
	        const p = ret[k];
	        const q = multiplyScalar(ret[k + len / 2], exp(multiplyScalar(multiplyScalar(tau, I), divideScalar(-k, len))));
	        ret[k] = addScalar(p, q);
	        ret[k + len / 2] = addScalar(p, multiplyScalar(-1, q));
	      }
	      return ret;
	    } else {
	      // use chirp-z transform for non-power-of-2 FFT
	      return _czt(arr);
	    }
	    // throw new Error('Can only calculate FFT of power-of-two size')
	  }
	});
	return fft$1;
}

var ifft$1 = {};

var hasRequiredIfft$1;

function requireIfft$1 () {
	if (hasRequiredIfft$1) return ifft$1;
	hasRequiredIfft$1 = 1;

	Object.defineProperty(ifft$1, "__esModule", {
	  value: true
	});
	ifft$1.createIfft = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'ifft';
	const dependencies = ['typed', 'fft', 'dotDivide', 'conj'];
	ifft$1.createIfft = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    fft,
	    dotDivide,
	    conj
	  } = _ref;
	  /**
	   * Calculate N-dimensional inverse Fourier transform
	   *
	   * Syntax:
	   *
	   *     math.ifft(arr)
	   *
	   * Examples:
	   *
	   *    math.ifft([[2, 2], [0, 0]]) // returns [[{re:1, im:0}, {re:0, im:0}], [{re:1, im:0}, {re:0, im:0}]]
	   *
	   * See Also:
	   *
	   *      fft
	   *
	   * @param {Array | Matrix} arr    An array or matrix
	   * @return {Array | Matrix}       N-dimensional Fourier transformation of the array
	   */
	  return typed(name, {
	    'Array | Matrix': function (arr) {
	      const size = (0, _is.isMatrix)(arr) ? arr.size() : (0, _array.arraySize)(arr);
	      return dotDivide(conj(fft(conj(arr))), size.reduce((acc, curr) => acc * curr, 1));
	    }
	  });
	});
	return ifft$1;
}

var solveODE$1 = {};

var hasRequiredSolveODE$1;

function requireSolveODE$1 () {
	if (hasRequiredSolveODE$1) return solveODE$1;
	hasRequiredSolveODE$1 = 1;

	Object.defineProperty(solveODE$1, "__esModule", {
	  value: true
	});
	solveODE$1.createSolveODE = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'solveODE';
	const dependencies = ['typed', 'add', 'subtract', 'multiply', 'divide', 'max', 'map', 'abs', 'isPositive', 'isNegative', 'larger', 'smaller', 'matrix', 'bignumber', 'unaryMinus'];
	solveODE$1.createSolveODE = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    subtract,
	    multiply,
	    divide,
	    max,
	    map,
	    abs,
	    isPositive,
	    isNegative,
	    larger,
	    smaller,
	    matrix,
	    bignumber,
	    unaryMinus
	  } = _ref;
	  /**
	     * Numerical Integration of Ordinary Differential Equations
	     *
	     * Two variable step methods are provided:
	     * - "RK23": Bogacki–Shampine method
	     * - "RK45": Dormand-Prince method RK5(4)7M (default)
	     *
	     * The arguments are expected as follows.
	     *
	     * - `func` should be the forcing function `f(t, y)`
	     * - `tspan` should be a vector of two numbers or units `[tStart, tEnd]`
	     * - `y0` the initial state values, should be a scalar or a flat array
	     * - `options` should be an object with the following information:
	     *   - `method` ('RK45'): ['RK23', 'RK45']
	     *   - `tol` (1e-3): Numeric tolerance of the method, the solver keeps the error estimates less than this value
	     *   - `firstStep`: Initial step size
	     *   - `minStep`: minimum step size of the method
	     *   - `maxStep`: maximum step size of the method
	     *   - `minDelta` (0.2): minimum ratio of change for the step
	     *   - `maxDelta` (5): maximum ratio of change for the step
	     *   - `maxIter` (1e4): maximum number of iterations
	     *
	     * The returned value is an object with `{t, y}` please note that even though `t` means time, it can represent any other independant variable like `x`:
	     * - `t` an array of size `[n]`
	     * - `y` the states array can be in two ways
	     *   - **if `y0` is a scalar:** returns an array-like of size `[n]`
	     *   - **if `y0` is a flat array-like of size [m]:** returns an array like of size `[n, m]`
	     *
	     * Syntax:
	     *
	     *     math.solveODE(func, tspan, y0)
	     *     math.solveODE(func, tspan, y0, options)
	     *
	     * Examples:
	     *
	     *     function func(t, y) {return y}
	     *     const tspan = [0, 4]
	     *     const y0 = 1
	     *     math.solveODE(func, tspan, y0)
	     *     math.solveODE(func, tspan, [1, 2])
	     *     math.solveODE(func, tspan, y0, { method:"RK23", maxStep:0.1 })
	     *
	     * See also:
	     *
	     *     derivative, simplifyCore
	     *
	     * @param {function} func The forcing function f(t,y)
	     * @param {Array | Matrix} tspan The time span
	     * @param {number | BigNumber | Unit | Array | Matrix} y0 The initial value
	     * @param {Object} [options] Optional configuration options
	     * @return {Object} Return an object with t and y values as arrays
	     */

	  function _rk(butcherTableau) {
	    // generates an adaptive runge kutta method from it's butcher tableau

	    return function (f, tspan, y0, options) {
	      // adaptive runge kutta methods
	      const wrongTSpan = !(tspan.length === 2 && (tspan.every(isNumOrBig) || tspan.every(_is.isUnit)));
	      if (wrongTSpan) {
	        throw new Error('"tspan" must be an Array of two numeric values or two units [tStart, tEnd]');
	      }
	      const t0 = tspan[0]; // initial time
	      const tf = tspan[1]; // final time
	      const isForwards = larger(tf, t0);
	      const firstStep = options.firstStep;
	      if (firstStep !== undefined && !isPositive(firstStep)) {
	        throw new Error('"firstStep" must be positive');
	      }
	      const maxStep = options.maxStep;
	      if (maxStep !== undefined && !isPositive(maxStep)) {
	        throw new Error('"maxStep" must be positive');
	      }
	      const minStep = options.minStep;
	      if (minStep && isNegative(minStep)) {
	        throw new Error('"minStep" must be positive or zero');
	      }
	      const timeVars = [t0, tf, firstStep, minStep, maxStep].filter(x => x !== undefined);
	      if (!(timeVars.every(isNumOrBig) || timeVars.every(_is.isUnit))) {
	        throw new Error('Inconsistent type of "t" dependant variables');
	      }
	      const steps = 1; // divide time in this number of steps
	      const tol = options.tol ? options.tol : 1e-4; // define a tolerance (must be an option)
	      const minDelta = options.minDelta ? options.minDelta : 0.2;
	      const maxDelta = options.maxDelta ? options.maxDelta : 5;
	      const maxIter = options.maxIter ? options.maxIter : 10000; // stop inifite evaluation if something goes wrong
	      const hasBigNumbers = [t0, tf, ...y0, maxStep, minStep].some(_is.isBigNumber);
	      const [a, c, b, bp] = hasBigNumbers ? [bignumber(butcherTableau.a), bignumber(butcherTableau.c), bignumber(butcherTableau.b), bignumber(butcherTableau.bp)] : [butcherTableau.a, butcherTableau.c, butcherTableau.b, butcherTableau.bp];
	      let h = firstStep ? isForwards ? firstStep : unaryMinus(firstStep) : divide(subtract(tf, t0), steps); // define the first step size
	      const t = [t0]; // start the time array
	      const y = [y0]; // start the solution array

	      const deltaB = subtract(b, bp); // b - bp

	      let n = 0;
	      let iter = 0;
	      const ongoing = _createOngoing(isForwards);
	      const trimStep = _createTrimStep(isForwards);
	      // iterate unitil it reaches either the final time or maximum iterations
	      while (ongoing(t[n], tf)) {
	        const k = [];

	        // trim the time step so that it doesn't overshoot
	        h = trimStep(t[n], tf, h);

	        // calculate the first value of k
	        k.push(f(t[n], y[n]));

	        // calculate the rest of the values of k
	        for (let i = 1; i < c.length; ++i) {
	          k.push(f(add(t[n], multiply(c[i], h)), add(y[n], multiply(h, a[i], k))));
	        }

	        // estimate the error by comparing solutions of different orders
	        const TE = max(abs(map(multiply(deltaB, k), X => (0, _is.isUnit)(X) ? X.value : X)));
	        if (TE < tol && tol / TE > 1 / 4) {
	          // push solution if within tol
	          t.push(add(t[n], h));
	          y.push(add(y[n], multiply(h, b, k)));
	          n++;
	        }

	        // estimate the delta value that will affect the step size
	        let delta = 0.84 * (tol / TE) ** (1 / 5);
	        if (smaller(delta, minDelta)) {
	          delta = minDelta;
	        } else if (larger(delta, maxDelta)) {
	          delta = maxDelta;
	        }
	        delta = hasBigNumbers ? bignumber(delta) : delta;
	        h = multiply(h, delta);
	        if (maxStep && larger(abs(h), maxStep)) {
	          h = isForwards ? maxStep : unaryMinus(maxStep);
	        } else if (minStep && smaller(abs(h), minStep)) {
	          h = isForwards ? minStep : unaryMinus(minStep);
	        }
	        iter++;
	        if (iter > maxIter) {
	          throw new Error('Maximum number of iterations reached, try changing options');
	        }
	      }
	      return {
	        t,
	        y
	      };
	    };
	  }
	  function _rk23(f, tspan, y0, options) {
	    // Bogacki–Shampine method

	    // Define the butcher table
	    const a = [[], [1 / 2], [0, 3 / 4], [2 / 9, 1 / 3, 4 / 9]];
	    const c = [null, 1 / 2, 3 / 4, 1];
	    const b = [2 / 9, 1 / 3, 4 / 9, 0];
	    const bp = [7 / 24, 1 / 4, 1 / 3, 1 / 8];
	    const butcherTableau = {
	      a,
	      c,
	      b,
	      bp
	    };

	    // Solve an adaptive step size rk method
	    return _rk(butcherTableau)(f, tspan, y0, options);
	  }
	  function _rk45(f, tspan, y0, options) {
	    // Dormand Prince method

	    // Define the butcher tableau
	    const a = [[], [1 / 5], [3 / 40, 9 / 40], [44 / 45, -56 / 15, 32 / 9], [19372 / 6561, -25360 / 2187, 64448 / 6561, -212 / 729], [9017 / 3168, -355 / 33, 46732 / 5247, 49 / 176, -5103 / 18656], [35 / 384, 0, 500 / 1113, 125 / 192, -2187 / 6784, 11 / 84]];
	    const c = [null, 1 / 5, 3 / 10, 4 / 5, 8 / 9, 1, 1];
	    const b = [35 / 384, 0, 500 / 1113, 125 / 192, -2187 / 6784, 11 / 84, 0];
	    const bp = [5179 / 57600, 0, 7571 / 16695, 393 / 640, -92097 / 339200, 187 / 2100, 1 / 40];
	    const butcherTableau = {
	      a,
	      c,
	      b,
	      bp
	    };

	    // Solve an adaptive step size rk method
	    return _rk(butcherTableau)(f, tspan, y0, options);
	  }
	  function _solveODE(f, tspan, y0, opt) {
	    const method = opt.method ? opt.method : 'RK45';
	    const methods = {
	      RK23: _rk23,
	      RK45: _rk45
	    };
	    if (method.toUpperCase() in methods) {
	      const methodOptions = {
	        ...opt
	      }; // clone the options object
	      delete methodOptions.method; // delete the method as it won't be needed
	      return methods[method.toUpperCase()](f, tspan, y0, methodOptions);
	    } else {
	      // throw an error indicating there is no such method
	      const methodsWithQuotes = Object.keys(methods).map(x => `"${x}"`);
	      // generates a string of methods like: "BDF", "RK23" and "RK45"
	      const availableMethodsString = `${methodsWithQuotes.slice(0, -1).join(', ')} and ${methodsWithQuotes.slice(-1)}`;
	      throw new Error(`Unavailable method "${method}". Available methods are ${availableMethodsString}`);
	    }
	  }
	  function _createOngoing(isForwards) {
	    // returns the correct function to test if it's still iterating
	    return isForwards ? smaller : larger;
	  }
	  function _createTrimStep(isForwards) {
	    const outOfBounds = isForwards ? larger : smaller;
	    return function (t, tf, h) {
	      const next = add(t, h);
	      return outOfBounds(next, tf) ? subtract(tf, t) : h;
	    };
	  }
	  function isNumOrBig(x) {
	    // checks if it's a number or bignumber
	    return (0, _is.isBigNumber)(x) || (0, _is.isNumber)(x);
	  }
	  function _matrixSolveODE(f, T, y0, options) {
	    // receives matrices and returns matrices
	    const sol = _solveODE(f, T.toArray(), y0.toArray(), options);
	    return {
	      t: matrix(sol.t),
	      y: matrix(sol.y)
	    };
	  }
	  return typed('solveODE', {
	    'function, Array, Array, Object': _solveODE,
	    'function, Matrix, Matrix, Object': _matrixSolveODE,
	    'function, Array, Array': (f, T, y0) => _solveODE(f, T, y0, {}),
	    'function, Matrix, Matrix': (f, T, y0) => _matrixSolveODE(f, T, y0, {}),
	    'function, Array, number | BigNumber | Unit': (f, T, y0) => {
	      const sol = _solveODE(f, T, [y0], {});
	      return {
	        t: sol.t,
	        y: sol.y.map(Y => Y[0])
	      };
	    },
	    'function, Matrix, number | BigNumber | Unit': (f, T, y0) => {
	      const sol = _solveODE(f, T.toArray(), [y0], {});
	      return {
	        t: matrix(sol.t),
	        y: matrix(sol.y.map(Y => Y[0]))
	      };
	    },
	    'function, Array, number | BigNumber | Unit, Object': (f, T, y0, options) => {
	      const sol = _solveODE(f, T, [y0], options);
	      return {
	        t: sol.t,
	        y: sol.y.map(Y => Y[0])
	      };
	    },
	    'function, Matrix, number | BigNumber | Unit, Object': (f, T, y0, options) => {
	      const sol = _solveODE(f, T.toArray(), [y0], options);
	      return {
	        t: matrix(sol.t),
	        y: matrix(sol.y.map(Y => Y[0]))
	      };
	    }
	  });
	});
	return solveODE$1;
}

var erf$1 = {};

var hasRequiredErf$1;

function requireErf$1 () {
	if (hasRequiredErf$1) return erf$1;
	hasRequiredErf$1 = 1;

	Object.defineProperty(erf$1, "__esModule", {
	  value: true
	});
	erf$1.createErf = void 0;
	var _collection = requireCollection();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	/* eslint-disable no-loss-of-precision */

	const name = 'erf';
	const dependencies = ['typed'];
	erf$1.createErf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the erf function of a value using a rational Chebyshev
	   * approximations for different intervals of x.
	   *
	   * This is a translation of W. J. Cody's Fortran implementation from 1987
	   * ( https://www.netlib.org/specfun/erf ). See the AMS publication
	   * "Rational Chebyshev Approximations for the Error Function" by W. J. Cody
	   * for an explanation of this process.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.erf(x)
	   *
	   * Examples:
	   *
	   *    math.erf(0.2)    // returns 0.22270258921047847
	   *    math.erf(-0.5)   // returns -0.5204998778130465
	   *    math.erf(4)      // returns 0.9999999845827421
	   *
	   * See also:
	   *    zeta
	   *
	   * @param {number | Array | Matrix} x   A real number
	   * @return {number | Array | Matrix}    The erf of `x`
	   */
	  return typed('name', {
	    number: function (x) {
	      const y = Math.abs(x);
	      if (y >= MAX_NUM) {
	        return (0, _number.sign)(x);
	      }
	      if (y <= THRESH) {
	        return (0, _number.sign)(x) * erf1(y);
	      }
	      if (y <= 4.0) {
	        return (0, _number.sign)(x) * (1 - erfc2(y));
	      }
	      return (0, _number.sign)(x) * (1 - erfc3(y));
	    },
	    'Array | Matrix': typed.referToSelf(self => n => (0, _collection.deepMap)(n, self))

	    // TODO: For complex numbers, use the approximation for the Faddeeva function
	    //  from "More Efficient Computation of the Complex Error Function" (AMS)
	  });

	  /**
	   * Approximates the error function erf() for x <= 0.46875 using this function:
	   *               n
	   * erf(x) = x * sum (p_j * x^(2j)) / (q_j * x^(2j))
	   *              j=0
	   */
	  function erf1(y) {
	    const ysq = y * y;
	    let xnum = P[0][4] * ysq;
	    let xden = ysq;
	    let i;
	    for (i = 0; i < 3; i += 1) {
	      xnum = (xnum + P[0][i]) * ysq;
	      xden = (xden + Q[0][i]) * ysq;
	    }
	    return y * (xnum + P[0][3]) / (xden + Q[0][3]);
	  }

	  /**
	   * Approximates the complement of the error function erfc() for
	   * 0.46875 <= x <= 4.0 using this function:
	   *                       n
	   * erfc(x) = e^(-x^2) * sum (p_j * x^j) / (q_j * x^j)
	   *                      j=0
	   */
	  function erfc2(y) {
	    let xnum = P[1][8] * y;
	    let xden = y;
	    let i;
	    for (i = 0; i < 7; i += 1) {
	      xnum = (xnum + P[1][i]) * y;
	      xden = (xden + Q[1][i]) * y;
	    }
	    const result = (xnum + P[1][7]) / (xden + Q[1][7]);
	    const ysq = parseInt(y * 16) / 16;
	    const del = (y - ysq) * (y + ysq);
	    return Math.exp(-ysq * ysq) * Math.exp(-del) * result;
	  }

	  /**
	   * Approximates the complement of the error function erfc() for x > 4.0 using
	   * this function:
	   *
	   * erfc(x) = (e^(-x^2) / x) * [ 1/sqrt(pi) +
	   *               n
	   *    1/(x^2) * sum (p_j * x^(-2j)) / (q_j * x^(-2j)) ]
	   *              j=0
	   */
	  function erfc3(y) {
	    let ysq = 1 / (y * y);
	    let xnum = P[2][5] * ysq;
	    let xden = ysq;
	    let i;
	    for (i = 0; i < 4; i += 1) {
	      xnum = (xnum + P[2][i]) * ysq;
	      xden = (xden + Q[2][i]) * ysq;
	    }
	    let result = ysq * (xnum + P[2][4]) / (xden + Q[2][4]);
	    result = (SQRPI - result) / y;
	    ysq = parseInt(y * 16) / 16;
	    const del = (y - ysq) * (y + ysq);
	    return Math.exp(-ysq * ysq) * Math.exp(-del) * result;
	  }
	});

	/**
	 * Upper bound for the first approximation interval, 0 <= x <= THRESH
	 * @constant
	 */
	const THRESH = 0.46875;

	/**
	 * Constant used by W. J. Cody's Fortran77 implementation to denote sqrt(pi)
	 * @constant
	 */
	const SQRPI = 5.6418958354775628695e-1;

	/**
	 * Coefficients for each term of the numerator sum (p_j) for each approximation
	 * interval (see W. J. Cody's paper for more details)
	 * @constant
	 */
	const P = [[3.16112374387056560e00, 1.13864154151050156e02, 3.77485237685302021e02, 3.20937758913846947e03, 1.85777706184603153e-1], [5.64188496988670089e-1, 8.88314979438837594e00, 6.61191906371416295e01, 2.98635138197400131e02, 8.81952221241769090e02, 1.71204761263407058e03, 2.05107837782607147e03, 1.23033935479799725e03, 2.15311535474403846e-8], [3.05326634961232344e-1, 3.60344899949804439e-1, 1.25781726111229246e-1, 1.60837851487422766e-2, 6.58749161529837803e-4, 1.63153871373020978e-2]];

	/**
	 * Coefficients for each term of the denominator sum (q_j) for each approximation
	 * interval (see W. J. Cody's paper for more details)
	 * @constant
	 */
	const Q = [[2.36012909523441209e01, 2.44024637934444173e02, 1.28261652607737228e03, 2.84423683343917062e03], [1.57449261107098347e01, 1.17693950891312499e02, 5.37181101862009858e02, 1.62138957456669019e03, 3.29079923573345963e03, 4.36261909014324716e03, 3.43936767414372164e03, 1.23033935480374942e03], [2.56852019228982242e00, 1.87295284992346047e00, 5.27905102951428412e-1, 6.05183413124413191e-2, 2.33520497626869185e-3]];

	/**
	 * Maximum/minimum safe numbers to input to erf() (in ES6+, this number is
	 * Number.[MAX|MIN]_SAFE_INTEGER). erf() for all numbers beyond this limit will
	 * return 1
	 */
	const MAX_NUM = Math.pow(2, 53);
	return erf$1;
}

var zeta$1 = {};

var hasRequiredZeta$1;

function requireZeta$1 () {
	if (hasRequiredZeta$1) return zeta$1;
	hasRequiredZeta$1 = 1;

	Object.defineProperty(zeta$1, "__esModule", {
	  value: true
	});
	zeta$1.createZeta = void 0;
	var _factory = requireFactory$b();
	const name = 'zeta';
	const dependencies = ['typed', 'config', 'multiply', 'pow', 'divide', 'factorial', 'equal', 'smallerEq', 'isBounded', 'isNegative', 'gamma', 'sin', 'subtract', 'add', '?Complex', '?BigNumber', 'pi'];
	zeta$1.createZeta = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    multiply,
	    pow,
	    divide,
	    factorial,
	    equal,
	    smallerEq,
	    isBounded,
	    isNegative,
	    gamma,
	    sin,
	    subtract,
	    add,
	    Complex,
	    BigNumber,
	    pi
	  } = _ref;
	  /**
	   * Compute the Riemann Zeta function of a value using an infinite series for
	   * all of the complex plane using Riemann's Functional equation.
	   *
	   * Based off the paper by Xavier Gourdon and Pascal Sebah
	   * ( http://numbers.computation.free.fr/Constants/Miscellaneous/zetaevaluations.pdf )
	   *
	   * Implementation and slight modification by Anik Patel
	   *
	   * Note: the implementation is accurate up to about 6 digits.
	   *
	   * Syntax:
	   *
	   *    math.zeta(n)
	   *
	   * Examples:
	   *
	   *    math.zeta(5)       // returns 1.0369277551433895
	   *    math.zeta(-0.5)    // returns -0.2078862249773449
	   *    math.zeta(math.i)  // returns 0.0033002236853253153 - 0.4181554491413212i
	   *
	   * See also:
	   *    erf
	   *
	   * @param {number | Complex | BigNumber} s   A Real, Complex or BigNumber parameter to the Riemann Zeta Function
	   * @return {number | Complex | BigNumber}    The Riemann Zeta of `s`
	   */
	  return typed(name, {
	    number: s => zetaNumeric(s, value => value, () => 20),
	    BigNumber: s => zetaNumeric(s, value => new BigNumber(value), () => {
	      // relTol is for example 1e-12. Extract the positive exponent 12 from that
	      return Math.abs(Math.log10(config.relTol));
	    }),
	    Complex: zetaComplex
	  });

	  /**
	   * @param {number | BigNumber} s
	   * @param {(value: number) => number | BigNumber} createValue
	   * @param {(value: number | BigNumber | Complex) => number} determineDigits
	   * @returns {number | BigNumber}
	   */
	  function zetaNumeric(s, createValue, determineDigits) {
	    if (equal(s, 0)) {
	      return createValue(-0.5);
	    }
	    if (equal(s, 1)) {
	      return createValue(NaN);
	    }
	    if (!isBounded(s)) {
	      return isNegative(s) ? createValue(NaN) : createValue(1);
	    }
	    return zeta(s, createValue, determineDigits, s => s);
	  }

	  /**
	   * @param {Complex} s
	   * @returns {Complex}
	   */
	  function zetaComplex(s) {
	    if (s.re === 0 && s.im === 0) {
	      return new Complex(-0.5);
	    }
	    if (s.re === 1) {
	      return new Complex(NaN, NaN);
	    }
	    if (s.re === Infinity && s.im === 0) {
	      return new Complex(1);
	    }
	    if (s.im === Infinity || s.re === -Infinity) {
	      return new Complex(NaN, NaN);
	    }
	    return zeta(s, value => value, s => Math.round(1.3 * 15 + 0.9 * Math.abs(s.im)), s => s.re);
	  }

	  /**
	   * @param {number | BigNumber | Complex} s
	   * @param {(value: number) => number | BigNumber | Complex} createValue
	   * @param {(value: number | BigNumber | Complex) => number} determineDigits
	   * @param {(value: number | BigNumber | Complex) => number} getRe
	   * @returns {*|number}
	   */
	  function zeta(s, createValue, determineDigits, getRe) {
	    const n = determineDigits(s);
	    if (getRe(s) > -(n - 1) / 2) {
	      return f(s, createValue(n), createValue);
	    } else {
	      // Function Equation for reflection to x < 1
	      let c = multiply(pow(2, s), pow(createValue(pi), subtract(s, 1)));
	      c = multiply(c, sin(multiply(divide(createValue(pi), 2), s)));
	      c = multiply(c, gamma(subtract(1, s)));
	      return multiply(c, zeta(subtract(1, s), createValue, determineDigits, getRe));
	    }
	  }

	  /**
	   * Calculate a portion of the sum
	   * @param {number | BigNumber} k   a positive integer
	   * @param {number | BigNumber} n   a positive integer
	   * @return {number}    the portion of the sum
	   **/
	  function d(k, n) {
	    let S = k;
	    for (let j = k; smallerEq(j, n); j = add(j, 1)) {
	      const factor = divide(multiply(factorial(add(n, subtract(j, 1))), pow(4, j)), multiply(factorial(subtract(n, j)), factorial(multiply(2, j))));
	      S = add(S, factor);
	    }
	    return multiply(n, S);
	  }

	  /**
	   * Calculate the positive Riemann Zeta function
	   * @param {number} s   a real or complex number with s.re > 1
	   * @param {number} n   a positive integer
	   * @param {(number) => number | BigNumber | Complex} createValue
	   * @return {number}    Riemann Zeta of s
	   **/
	  function f(s, n, createValue) {
	    const c = divide(1, multiply(d(createValue(0), n), subtract(1, pow(2, subtract(1, s)))));
	    let S = createValue(0);
	    for (let k = createValue(1); smallerEq(k, n); k = add(k, 1)) {
	      S = add(S, divide(multiply((-1) ** (k - 1), d(k, n)), pow(k, s)));
	    }
	    return multiply(c, S);
	  }
	});
	return zeta$1;
}

var mode$1 = {};

var hasRequiredMode$1;

function requireMode$1 () {
	if (hasRequiredMode$1) return mode$1;
	hasRequiredMode$1 = 1;

	Object.defineProperty(mode$1, "__esModule", {
	  value: true
	});
	mode$1.createMode = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'mode';
	const dependencies = ['typed', 'isNaN', 'isNumeric'];
	mode$1.createMode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isNaN: mathIsNaN,
	    isNumeric
	  } = _ref;
	  /**
	  * Computes the mode of a set of numbers or a list with values(numbers or characters).
	  * If there are multiple modes, it returns a list of those values.
	  *
	  * Syntax:
	  *
	  *     math.mode(a, b, c, ...)
	  *     math.mode(A)
	  *
	  * Examples:
	  *
	  *     math.mode(2, 1, 4, 3, 1)                            // returns [1]
	  *     math.mode([1, 2.7, 3.2, 4, 2.7])                    // returns [2.7]
	  *     math.mode(1, 4, 6, 1, 6)                             // returns [1, 6]
	  *     math.mode('a','a','b','c')                           // returns ["a"]
	  *     math.mode(1, 1.5, 'abc')                             // returns [1, 1.5, "abc"]
	  *
	  * See also:
	  *
	  *     median,
	  *     mean
	  *
	  * @param {... *} args  A single matrix
	  * @return {*} The mode of all values
	  */
	  return typed(name, {
	    'Array | Matrix': _mode,
	    '...': function (args) {
	      return _mode(args);
	    }
	  });

	  /**
	   * Calculates the mode in an 1-dimensional array
	   * @param {Array} values
	   * @return {Array} mode
	   * @private
	   */
	  function _mode(values) {
	    values = (0, _array.flatten)(values.valueOf());
	    const num = values.length;
	    if (num === 0) {
	      throw new Error('Cannot calculate mode of an empty array');
	    }
	    const count = {};
	    let mode = [];
	    let max = 0;
	    for (let i = 0; i < values.length; i++) {
	      const value = values[i];
	      if (isNumeric(value) && mathIsNaN(value)) {
	        throw new Error('Cannot calculate mode of an array containing NaN values');
	      }
	      if (!(value in count)) {
	        count[value] = 0;
	      }
	      count[value]++;
	      if (count[value] === max) {
	        mode.push(value);
	      } else if (count[value] > max) {
	        max = count[value];
	        mode = [value];
	      }
	    }
	    return mode;
	  }
	});
	return mode$1;
}

var prod$1 = {};

var improveErrorMessage = {};

var hasRequiredImproveErrorMessage;

function requireImproveErrorMessage () {
	if (hasRequiredImproveErrorMessage) return improveErrorMessage;
	hasRequiredImproveErrorMessage = 1;

	Object.defineProperty(improveErrorMessage, "__esModule", {
	  value: true
	});
	improveErrorMessage.improveErrorMessage = improveErrorMessage$1;
	var _is = requireIs();
	/**
	 * Improve error messages for statistics functions. Errors are typically
	 * thrown in an internally used function like larger, causing the error
	 * not to mention the function (like max) which is actually used by the user.
	 *
	 * @param {Error} err
	 * @param {String} fnName
	 * @param {*} [value]
	 * @return {Error}
	 */
	function improveErrorMessage$1(err, fnName, value) {
	  // TODO: add information with the index (also needs transform in expression parser)
	  let details;
	  if (String(err).includes('Unexpected type')) {
	    details = arguments.length > 2 ? ' (type: ' + (0, _is.typeOf)(value) + ', value: ' + JSON.stringify(value) + ')' : ' (type: ' + err.data.actual + ')';
	    return new TypeError('Cannot calculate ' + fnName + ', unexpected type of argument' + details);
	  }
	  if (String(err).includes('complex numbers')) {
	    details = arguments.length > 2 ? ' (type: ' + (0, _is.typeOf)(value) + ', value: ' + JSON.stringify(value) + ')' : '';
	    return new TypeError('Cannot calculate ' + fnName + ', no ordering relation is defined for complex numbers' + details);
	  }
	  return err;
	}
	return improveErrorMessage;
}

var hasRequiredProd$1;

function requireProd$1 () {
	if (hasRequiredProd$1) return prod$1;
	hasRequiredProd$1 = 1;

	Object.defineProperty(prod$1, "__esModule", {
	  value: true
	});
	prod$1.createProd = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'prod';
	const dependencies = ['typed', 'config', 'multiplyScalar', 'numeric'];
	prod$1.createProd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    multiplyScalar,
	    numeric
	  } = _ref;
	  /**
	   * Compute the product of a matrix or a list with values.
	   * In case of a multidimensional array or matrix, the sum of all
	   * elements will be calculated.
	   *
	   * Syntax:
	   *
	   *     math.prod(a, b, c, ...)
	   *     math.prod(A)
	   *
	   * Examples:
	   *
	   *     math.multiply(2, 3)           // returns 6
	   *     math.prod(2, 3)               // returns 6
	   *     math.prod(2, 3, 4)            // returns 24
	   *     math.prod([2, 3, 4])          // returns 24
	   *     math.prod([[2, 5], [4, 3]])   // returns 120
	   *
	   * See also:
	   *
	   *    mean, median, min, max, sum, std, variance
	   *
	   * @param {... *} args  A single matrix or or multiple scalar values
	   * @return {*} The product of all values
	   */
	  return typed(name, {
	    // prod([a, b, c, d, ...])
	    'Array | Matrix': _prod,
	    // prod([a, b, c, d, ...], dim)
	    'Array | Matrix, number | BigNumber': function (array, dim) {
	      // TODO: implement prod(A, dim)
	      throw new Error('prod(A, dim) is not yet supported');
	      // return reduce(arguments[0], arguments[1], math.prod)
	    },
	    // prod(a, b, c, d, ...)
	    '...': function (args) {
	      return _prod(args);
	    }
	  });

	  /**
	   * Recursively calculate the product of an n-dimensional array
	   * @param {Array} array
	   * @return {number} prod
	   * @private
	   */
	  function _prod(array) {
	    let prod;
	    (0, _collection.deepForEach)(array, function (value) {
	      try {
	        prod = prod === undefined ? value : multiplyScalar(prod, value);
	      } catch (err) {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'prod', value);
	      }
	    });

	    // make sure returning numeric value: parse a string into a numeric value
	    if (typeof prod === 'string') {
	      prod = numeric(prod, (0, _number.safeNumberType)(prod, config));
	    }
	    if (prod === undefined) {
	      throw new Error('Cannot calculate prod of an empty array');
	    }
	    return prod;
	  }
	});
	return prod$1;
}

var format$2 = {};

var hasRequiredFormat$1;

function requireFormat$1 () {
	if (hasRequiredFormat$1) return format$2;
	hasRequiredFormat$1 = 1;

	Object.defineProperty(format$2, "__esModule", {
	  value: true
	});
	format$2.createFormat = void 0;
	var _string = requireString$2();
	var _factory = requireFactory$b();
	const name = 'format';
	const dependencies = ['typed'];
	format$2.createFormat = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Format a value of any type into a string.
	   *
	   * Syntax:
	   *
	   *    math.format(value)
	   *    math.format(value, options)
	   *    math.format(value, precision)
	   *    math.format(value, callback)
	   *
	   * Where:
	   *
	   *  - `value: *`
	   *    The value to be formatted
	   *  - `options: Object`
	   *    An object with formatting options. Available options:
	   *    - `notation: string`
	   *      Number notation. Choose from:
	   *      - `'fixed'`
	   *        Always use regular number notation.
	   *        For example `'123.40'` and `'14000000'`
	   *      - `'exponential'`
	   *        Always use exponential notation.
	   *        For example `'1.234e+2'` and `'1.4e+7'`
	   *      - `'engineering'`
	   *        Always use engineering notation: always have exponential notation,
	   *        and select the exponent to be a multiple of `3`.
	   *        For example `'123.4e+0'` and `'14.0e+6'`
	   *      - `'auto'` (default)
	   *        Regular number notation for numbers having an absolute value between
	   *        `lower` and `upper` bounds, and uses exponential notation elsewhere.
	   *        Lower bound is included, upper bound is excluded.
	   *        For example `'123.4'` and `'1.4e7'`.
	   *      - `'bin'`, `'oct'`, or `'hex'`
	   *        Format the number using binary, octal, or hexadecimal notation.
	   *        For example `'0b1101'` and `'0x10fe'`.
	   *    - `wordSize: number | BigNumber`
	   *      The word size in bits to use for formatting in binary, octal, or
	   *      hexadecimal notation. To be used only with `'bin'`, `'oct'`, or `'hex'`
	   *      values for `notation` option. When this option is defined the value
	   *      is formatted as a signed twos complement integer of the given word
	   *      size and the size suffix is appended to the output.
	   *      For example `format(-1, {notation: 'hex', wordSize: 8}) === '0xffi8'`.
	   *      Default value is undefined.
	   *    - `precision: number | BigNumber`
	   *      Limit the number of digits of the formatted value.
	   *      For regular numbers, must be a number between `0` and `16`.
	   *      For bignumbers, the maximum depends on the configured precision,
	   *      see function `config()`.
	   *      In case of notations `'exponential'`, `'engineering'`, and `'auto'`,
	   *      `precision` defines the total number of significant digits returned.
	   *      In case of notation `'fixed'`, `precision` defines the number of
	   *      significant digits after the decimal point.
	   *      `precision` is undefined by default.
	   *    - `lowerExp: number`
	   *      Exponent determining the lower boundary for formatting a value with
	   *      an exponent when `notation='auto'`. Default value is `-3`.
	   *    - `upperExp: number`
	   *      Exponent determining the upper boundary for formatting a value with
	   *      an exponent when `notation='auto'`. Default value is `5`.
	   *    - `fraction: string`. Available values: `'ratio'` (default) or `'decimal'`.
	   *      For example `format(fraction(1, 3))` will output `'1/3'` when `'ratio'`
	   *      is configured, and will output `'0.(3)'` when `'decimal'` is configured.
	   *    - `truncate: number`. Specifies the maximum allowed length of the
	   *      returned string. If it had been longer, the excess characters
	   *      are deleted and replaced with `'...'`.
	   * - `callback: function`
	   *   A custom formatting function, invoked for all numeric elements in `value`,
	   *   for example all elements of a matrix, or the real and imaginary
	   *   parts of a complex number. This callback can be used to override the
	   *   built-in numeric notation with any type of formatting. Function `callback`
	   *   is called with `value` as parameter and must return a string.
	   *
	   * When `value` is an Object:
	   *
	   * - When the object contains a property `format` being a function, this function
	   *   is invoked as `value.format(options)` and the result is returned.
	   * - When the object has its own `toString` method, this method is invoked
	   *   and the result is returned.
	   * - In other cases the function will loop over all object properties and
	   *   return JSON object notation like '{"a": 2, "b": 3}'.
	   *
	   * When value is a function:
	   *
	   * - When the function has a property `syntax`, it returns this
	   *   syntax description.
	   * - In other cases, a string `'function'` is returned.
	   *
	   * Examples:
	   *
	   *    math.format(6.4)                                        // returns '6.4'
	   *    math.format(1240000)                                    // returns '1.24e+6'
	   *    math.format(1/3)                                        // returns '0.3333333333333333'
	   *    math.format(1/3, 3)                                     // returns '0.333'
	   *    math.format(21385, 2)                                   // returns '21000'
	   *    math.format(12e8, {notation: 'fixed'})                  // returns '1200000000'
	   *    math.format(2.3,  {notation: 'fixed', precision: 4})    // returns '2.3000'
	   *    math.format(52.8, {notation: 'exponential'})            // returns '5.28e+1'
	   *    math.format(12400, {notation: 'engineering'})           // returns '12.4e+3'
	   *    math.format(2000, {lowerExp: -2, upperExp: 2})          // returns '2e+3'
	   *
	   *    function formatCurrency(value) {
	   *      // return currency notation with two digits:
	   *      return '$' + value.toFixed(2)
	   *
	   *      // you could also use math.format inside the callback:
	   *      // return '$' + math.format(value, {notation: 'fixed', precision: 2})
	   *    }
	   *    math.format([2.1, 3, 0.016], formatCurrency)            // returns '[$2.10, $3.00, $0.02]'
	   *
	   * See also:
	   *
	   *    print
	   *
	   * History:
	   *
	   *    v0.4     Created
	   *    v0.7     Round to a consistent number of digits (rather than decimals)
	   *    v0.15    Added multiple number notations and configurable precision
	   *    v3       Added support for JSON objects
	   *    v9       Added binary, hexadecimal, and octal notations
	   *    v10.4.2  Add `truncate` option
	   *
	   * @param {*} value                               Value to be stringified
	   * @param {Object | Function | number} [options]  Formatting options
	   * @return {string} The formatted value
	   */
	  return typed(name, {
	    any: _string.format,
	    'any, Object | function | number | BigNumber': _string.format
	  });
	});
	return format$2;
}

var bin$1 = {};

var hasRequiredBin$1;

function requireBin$1 () {
	if (hasRequiredBin$1) return bin$1;
	hasRequiredBin$1 = 1;

	Object.defineProperty(bin$1, "__esModule", {
	  value: true
	});
	bin$1.createBin = void 0;
	var _factory = requireFactory$b();
	const name = 'bin';
	const dependencies = ['typed', 'format'];

	/**
	 * Format a number as binary.
	 *
	 * Syntax:
	 *
	 *    math.bin(value)
	 *
	 * Examples:
	 *
	 *    //the following outputs "0b10"
	 *    math.bin(2)
	 *
	 * See also:
	 *
	 *    oct
	 *    hex
	 *
	 * @param {number | BigNumber} value    Value to be stringified
	 * @param {number | BigNumber} wordSize Optional word size (see `format`)
	 * @return {string}         The formatted value
	 */
	bin$1.createBin = (0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    format
	  } = _ref;
	  return typed(name, {
	    'number | BigNumber': function (n) {
	      return format(n, {
	        notation: 'bin'
	      });
	    },
	    'number | BigNumber, number | BigNumber': function (n, wordSize) {
	      return format(n, {
	        notation: 'bin',
	        wordSize
	      });
	    }
	  });
	});
	return bin$1;
}

var oct$1 = {};

var hasRequiredOct$1;

function requireOct$1 () {
	if (hasRequiredOct$1) return oct$1;
	hasRequiredOct$1 = 1;

	Object.defineProperty(oct$1, "__esModule", {
	  value: true
	});
	oct$1.createOct = void 0;
	var _factory = requireFactory$b();
	const name = 'oct';
	const dependencies = ['typed', 'format'];

	/**
	 * Format a number as octal.
	 *
	 * Syntax:
	 *
	 *    math.oct(value)
	 *
	 * Examples:
	 *
	 *    //the following outputs "0o70"
	 *    math.oct(56)
	 *
	 * See also:
	 *
	 *    bin
	 *    hex
	 *
	 * @param {number | BigNumber} value    Value to be stringified
	 * @param {number | BigNumber} wordSize Optional word size (see `format`)
	 * @return {string}         The formatted value
	 */

	oct$1.createOct = (0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    format
	  } = _ref;
	  return typed(name, {
	    'number | BigNumber': function (n) {
	      return format(n, {
	        notation: 'oct'
	      });
	    },
	    'number | BigNumber, number | BigNumber': function (n, wordSize) {
	      return format(n, {
	        notation: 'oct',
	        wordSize
	      });
	    }
	  });
	});
	return oct$1;
}

var hex$1 = {};

var hasRequiredHex$1;

function requireHex$1 () {
	if (hasRequiredHex$1) return hex$1;
	hasRequiredHex$1 = 1;

	Object.defineProperty(hex$1, "__esModule", {
	  value: true
	});
	hex$1.createHex = void 0;
	var _factory = requireFactory$b();
	const name = 'hex';
	const dependencies = ['typed', 'format'];

	/**
	 * Format a number as hexadecimal.
	 *
	 * Syntax:
	 *
	 *    math.hex(value)
	 *
	 * Examples:
	 *
	 *    math.hex(240) // returns "0xf0"
	 *
	 * See also:
	 *
	 *    oct
	 *    bin
	 *
	 * @param {number | BigNumber} value    Value to be stringified
	 * @param {number | BigNumber} wordSize Optional word size (see `format`)
	 * @return {string}         The formatted value
	 */
	hex$1.createHex = (0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    format
	  } = _ref;
	  return typed(name, {
	    'number | BigNumber': function (n) {
	      return format(n, {
	        notation: 'hex'
	      });
	    },
	    'number | BigNumber, number | BigNumber': function (n, wordSize) {
	      return format(n, {
	        notation: 'hex',
	        wordSize
	      });
	    }
	  });
	});
	return hex$1;
}

var print$2 = {};

var print$1 = {};

var hasRequiredPrint$2;

function requirePrint$2 () {
	if (hasRequiredPrint$2) return print$1;
	hasRequiredPrint$2 = 1;

	Object.defineProperty(print$1, "__esModule", {
	  value: true
	});
	print$1.printTemplate = void 0;
	print$1.printTemplate = /\$([\w.]+)/g;
	return print$1;
}

var hasRequiredPrint$1;

function requirePrint$1 () {
	if (hasRequiredPrint$1) return print$2;
	hasRequiredPrint$1 = 1;

	Object.defineProperty(print$2, "__esModule", {
	  value: true
	});
	print$2.createPrint = void 0;
	var _string = requireString$2();
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _print2 = requirePrint$2();
	const name = 'print';
	const dependencies = ['typed'];
	print$2.createPrint = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Interpolate values into a string template.
	   *
	   * Syntax:
	   *
	   *     math.print(template, values)
	   *     math.print(template, values, precision)
	   *     math.print(template, values, options)
	   *
	   * Example usage:
	   *
	   *     // the following outputs: 'Lucy is 5 years old'
	   *     math.print('Lucy is $age years old', {age: 5})
	   *
	   *     // the following outputs: 'The value of pi is 3.141592654'
	   *     math.print('The value of pi is $pi', {pi: math.pi}, 10)
	   *
	   *     // the following outputs: 'Hello Mary! The date is 2013-03-23'
	   *     math.print('Hello $user.name! The date is $date', {
	   *       user: {
	   *         name: 'Mary',
	   *       },
	   *       date: '2013-03-23'
	   *     })
	   *
	   *     // the following outputs: 'My favorite fruits are apples and bananas !'
	   *     math.print('My favorite fruits are $0 and $1 !', [
	   *       'apples',
	   *       'bananas'
	   *     ])
	   *
	   * See also:
	   *
	   *     format
	   *
	   * @param {string} template           A string containing variable placeholders.
	   * @param {Object | Array | Matrix}   values An object or array containing variables
	   *                                    which will be filled in in the template.
	   * @param {number | Object} [options] Formatting options,
	   *                                    or the number of digits to format numbers.
	   *                                    See function math.format for a description
	   *                                    of all options.
	   * @return {string} Interpolated string
	   */
	  return typed(name, {
	    // note: Matrix will be converted automatically to an Array
	    'string, Object | Array': _print,
	    'string, Object | Array, number | Object': _print
	  });
	});

	/**
	 * Interpolate values into a string template.
	 * @param {string} template
	 * @param {Object} values
	 * @param {number | Object} [options]
	 * @returns {string} Interpolated string
	 * @private
	 */
	function _print(template, values, options) {
	  return template.replace(_print2.printTemplate, function (original, key) {
	    const keys = key.split('.');
	    let value = values[keys.shift()];
	    if (value !== undefined && value.isMatrix) {
	      value = value.toArray();
	    }
	    while (keys.length && value !== undefined) {
	      const k = keys.shift();
	      value = k ? value[k] : value + '.';
	    }
	    if (value !== undefined) {
	      if (!(0, _is.isString)(value)) {
	        return (0, _string.format)(value, options);
	      } else {
	        return value;
	      }
	    }
	    return original;
	  });
	}
	return print$2;
}

var to$1 = {};

var hasRequiredTo$1;

function requireTo$1 () {
	if (hasRequiredTo$1) return to$1;
	hasRequiredTo$1 = 1;

	Object.defineProperty(to$1, "__esModule", {
	  value: true
	});
	to$1.createTo = void 0;
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'to';
	const dependencies = ['typed', 'matrix', 'concat'];
	to$1.createTo = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    concat
	  } = _ref;
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Change the unit of a value.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.to(x, unit)
	   *
	   * Examples:
	   *
	   *    math.to(math.unit('2 inch'), 'cm')             // returns Unit 5.08 cm
	   *    math.to(math.unit('2 inch'), math.unit('cm'))  // returns Unit 5.08 cm
	   *    math.to(math.unit(16, 'bytes'), 'bits')        // returns Unit 128 bits
	   *
	   * See also:
	   *
	   *    unit
	   *
	   * @param {Unit | Array | Matrix} x     The unit to be converted.
	   * @param {Unit | Array | Matrix} unit  New unit. Can be a string like "cm"
	   *                                      or a unit without value.
	   * @return {Unit | Array | Matrix} value with changed, fixed unit.
	   */
	  return typed(name, {
	    'Unit, Unit | string': (x, unit) => x.to(unit)
	  }, matrixAlgorithmSuite({
	    Ds: true
	  }));
	});
	return to$1;
}

var toBest$1 = {};

var hasRequiredToBest$1;

function requireToBest$1 () {
	if (hasRequiredToBest$1) return toBest$1;
	hasRequiredToBest$1 = 1;

	Object.defineProperty(toBest$1, "__esModule", {
	  value: true
	});
	toBest$1.createToBest = void 0;
	var _factory = requireFactory$b();
	const name = 'toBest';
	const dependencies = ['typed'];
	toBest$1.createToBest = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Converts a unit to the most appropriate display unit.
	   * When no preferred units are provided, the function automatically find the best prefix.
	   * When preferred units are provided, it converts to
	   * the unit that gives a value closest to 1.
	   *
	   * Syntax:
	   *
	   *    math.toBest(unit)
	   *    math.toBest(unit, unitList)
	   *    math.toBest(unit, unitList, options)
	   *
	   * Where:
	   * - `unitList` is an optional array of preferred target units as string or Unit.
	   * - `options` is an optional object with options, formed as follows:
	   * - `offset`: number | BigNumber
	   *
	   * Examples:
	   *
	   *   math.unit(0.05, 'm').toBest(['cm', 'mm'])                 // returns Unit 5 cm
	   *   math.unit(2 / 3, 'cm').toBest()                           // returns Unit 0.6666666666666666 cm
	   *   math.unit(10, 'm').toBest(['mm', 'km'], { offset: 1.5 })  // returns Unit 10000 mm
	   *
	   * See also:
	   *
	   *    unit, to, format
	   *
	   * @param {Unit} x                          The unit to be converted
	   * @param {Array<string>} [unitList=[]]     Optional array of preferred target units
	   * @param {Object} [options]                Optional options object
	   * @return {Unit}                           Value converted to the best matching unit
	   */
	  return typed(name, {
	    Unit: x => x.toBest(),
	    'Unit, string': (x, unitList) => x.toBest(unitList.split(',')),
	    'Unit, string, Object': (x, unitList, options) => x.toBest(unitList.split(','), options),
	    'Unit, Array': (x, unitList) => x.toBest(unitList),
	    'Unit, Array, Object': (x, unitList, options) => x.toBest(unitList, options)
	  });
	});
	return toBest$1;
}

var isPrime$1 = {};

var hasRequiredIsPrime$1;

function requireIsPrime$1 () {
	if (hasRequiredIsPrime$1) return isPrime$1;
	hasRequiredIsPrime$1 = 1;

	Object.defineProperty(isPrime$1, "__esModule", {
	  value: true
	});
	isPrime$1.createIsPrime = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'isPrime';
	const dependencies = ['typed'];
	isPrime$1.createIsPrime = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Test whether a value is prime: has no divisors other than itself and one.
	   * The function supports type `number`, `bignumber`.
	   *
	   * The function is evaluated element-wise in case of Array or Matrix input.
	   *
	   * Syntax:
	   *
	   *     math.isPrime(x)
	   *
	   * Examples:
	   *
	   *    math.isPrime(3)                     // returns true
	   *    math.isPrime(-2)                    // returns false
	   *    math.isPrime(0)                     // returns false
	   *    math.isPrime(-0)                    // returns false
	   *    math.isPrime(0.5)                   // returns false
	   *    math.isPrime('2')                   // returns true
	   *    math.isPrime([2, 17, 100])           // returns [true, true, false]
	   *
	   * See also:
	   *
	   *    isNumeric, isZero, isNegative, isInteger
	   *
	   * @param {number | BigNumber | bigint | Array | Matrix} x  Value to be tested
	   * @return {boolean}  Returns true when `x` is larger than zero.
	   *                    Throws an error in case of an unknown data type.
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x <= 3) {
	        return x > 1;
	      }
	      if (x % 2 === 0 || x % 3 === 0) {
	        return false;
	      }
	      for (let i = 5; i * i <= x; i += 6) {
	        if (x % i === 0 || x % (i + 2) === 0) {
	          return false;
	        }
	      }
	      return true;
	    },
	    bigint: function (x) {
	      if (x <= 3n) {
	        return x > 1n;
	      }
	      if (x % 2n === 0n || x % 3n === 0n) {
	        return false;
	      }
	      for (let i = 5n; i * i <= x; i += 6n) {
	        if (x % i === 0n || x % (i + 2n) === 0n) {
	          return false;
	        }
	      }
	      return true;
	    },
	    BigNumber: function (n) {
	      if (n.lte(3)) return n.gt(1);
	      if (n.mod(2).eq(0) || n.mod(3).eq(0)) return false;
	      if (n.lt(Math.pow(2, 32))) {
	        const x = n.toNumber();
	        for (let i = 5; i * i <= x; i += 6) {
	          if (x % i === 0 || x % (i + 2) === 0) {
	            return false;
	          }
	        }
	        return true;
	      }
	      function modPow(base, exponent, modulus) {
	        // exponent can be huge, use non-recursive variant
	        let accumulator = 1;
	        while (!exponent.eq(0)) {
	          if (exponent.mod(2).eq(0)) {
	            exponent = exponent.div(2);
	            base = base.mul(base).mod(modulus);
	          } else {
	            exponent = exponent.sub(1);
	            accumulator = base.mul(accumulator).mod(modulus);
	          }
	        }
	        return accumulator;
	      }

	      // https://en.wikipedia.org/wiki/Miller%E2%80%93Rabin_primality_test#Deterministic_variants
	      const Decimal = n.constructor.clone({
	        precision: n.toFixed(0).length * 2
	      });
	      n = new Decimal(n);
	      let r = 0;
	      let d = n.sub(1);
	      while (d.mod(2).eq(0)) {
	        d = d.div(2);
	        r += 1;
	      }
	      let bases = null;
	      // https://en.wikipedia.org/wiki/Miller–Rabin_primality_test#Testing_against_small_sets_of_bases
	      if (n.lt('3317044064679887385961981')) {
	        bases = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41].filter(x => x < n);
	      } else {
	        const max = Math.min(n.toNumber() - 2, Math.floor(2 * Math.pow(n.toFixed(0).length * Math.log(10), 2)));
	        bases = [];
	        for (let i = 2; i <= max; i += 1) {
	          bases.push(max);
	        }
	      }
	      for (let i = 0; i < bases.length; i += 1) {
	        const a = bases[i];
	        const adn = modPow(n.sub(n).add(a), d, n);
	        if (!adn.eq(1)) {
	          for (let i = 0, x = adn; !x.eq(n.sub(1)); i += 1, x = x.mul(x).mod(n)) {
	            if (i === r - 1) {
	              return false;
	            }
	          }
	        }
	      }
	      return true;
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return isPrime$1;
}

var numeric$1 = {};

var hasRequiredNumeric$1;

function requireNumeric$1 () {
	if (hasRequiredNumeric$1) return numeric$1;
	hasRequiredNumeric$1 = 1;

	Object.defineProperty(numeric$1, "__esModule", {
	  value: true
	});
	numeric$1.createNumeric = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _noop = requireNoop();
	const name = 'numeric';
	const dependencies = ['number', '?bignumber', '?fraction'];
	numeric$1.createNumeric = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    number,
	    bignumber,
	    fraction
	  } = _ref;
	  const validInputTypes = {
	    string: true,
	    number: true,
	    BigNumber: true,
	    Fraction: true
	  };

	  // Load the conversion functions for each output type
	  const validOutputTypes = {
	    number: x => number(x),
	    BigNumber: bignumber ? x => bignumber(x) : _noop.noBignumber,
	    bigint: x => BigInt(x),
	    Fraction: fraction ? x => fraction(x) : _noop.noFraction
	  };

	  /**
	   * Convert a numeric input to a specific numeric type: number, BigNumber, bigint, or Fraction.
	   *
	   * Syntax:
	   *
	   *    math.numeric(x)
	   *    math.numeric(value, outputType)
	   *
	   * Examples:
	   *
	   *    math.numeric('4')                           // returns 4
	   *    math.numeric('4', 'number')                 // returns 4
	   *    math.numeric('4', 'bigint')                 // returns 4n
	   *    math.numeric('4', 'BigNumber')              // returns BigNumber 4
	   *    math.numeric('4', 'Fraction')               // returns Fraction 4
	   *    math.numeric(4, 'Fraction')                 // returns Fraction 4
	   *    math.numeric(math.fraction(2, 5), 'number') // returns 0.4
	   *
	   * See also:
	   *
	   *    number, fraction, bignumber, bigint, string, format
	   *
	   * History:
	   *
	   *    v6       Created
	   *    v13      Added `bigint` support
	   *    v14.2.1  Prefer mathjs `bigint()` to built-in `BigInt()`
	   *
	   * @param {string | number | BigNumber | bigint | Fraction } value
	   *              A numeric value or a string containing a numeric value
	   * @param {string} outputType
	   *              Desired numeric output type.
	   *              Available values: 'number', 'BigNumber', or 'Fraction'
	   * @return {number | BigNumber | bigint | Fraction}
	   *              Returns an instance of the numeric in the requested type
	   */
	  return function numeric(value) {
	    let outputType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'number';
	    let check = arguments.length > 2 ? arguments[2] : undefined;
	    if (check !== undefined) {
	      throw new SyntaxError('numeric() takes one or two arguments');
	    }
	    const inputType = (0, _is.typeOf)(value);
	    if (!(inputType in validInputTypes)) {
	      throw new TypeError('Cannot convert ' + value + ' of type "' + inputType + '"; valid input types are ' + Object.keys(validInputTypes).join(', '));
	    }
	    if (!(outputType in validOutputTypes)) {
	      throw new TypeError('Cannot convert ' + value + ' to type "' + outputType + '"; valid output types are ' + Object.keys(validOutputTypes).join(', '));
	    }
	    if (outputType === inputType) {
	      return value;
	    } else {
	      return validOutputTypes[outputType](value);
	    }
	  };
	});
	return numeric$1;
}

var divideScalar = {};

var hasRequiredDivideScalar;

function requireDivideScalar () {
	if (hasRequiredDivideScalar) return divideScalar;
	hasRequiredDivideScalar = 1;

	Object.defineProperty(divideScalar, "__esModule", {
	  value: true
	});
	divideScalar.createDivideScalar = void 0;
	var _factory = requireFactory$b();
	const name = 'divideScalar';
	const dependencies = ['typed', 'numeric'];
	divideScalar.createDivideScalar = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    numeric
	  } = _ref;
	  /**
	   * Divide two scalar values, `x / y`.
	   * This function is meant for internal use: it is used by the public functions
	   * `divide` and `inv`.
	   *
	   * This function does not support collections (Array or Matrix).
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit} x   Numerator
	   * @param  {number | BigNumber | bigint | Fraction | Complex} y          Denominator
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit}     Quotient, `x / y`
	   * @private
	   */
	  return typed(name, {
	    'number, number': function (x, y) {
	      return x / y;
	    },
	    'Complex, Complex': function (x, y) {
	      return x.div(y);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return x.div(y);
	    },
	    'bigint, bigint': function (x, y) {
	      return x / y;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return x.div(y);
	    },
	    'Unit, number | Complex | Fraction | BigNumber | Unit': (x, y) => x.divide(y),
	    'number | Fraction | Complex | BigNumber, Unit': (x, y) => y.divideInto(x)
	  });
	});
	return divideScalar;
}

var pow$1 = {};

var hasRequiredPow$1;

function requirePow$1 () {
	if (hasRequiredPow$1) return pow$1;
	hasRequiredPow$1 = 1;

	Object.defineProperty(pow$1, "__esModule", {
	  value: true
	});
	pow$1.createPow = void 0;
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _array = requireArray();
	var _index = requireNumber$2();
	const name = 'pow';
	const dependencies = ['typed', 'config', 'identity', 'multiply', 'matrix', 'inv', 'fraction', 'number', 'Complex'];
	pow$1.createPow = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    identity,
	    multiply,
	    matrix,
	    inv,
	    number,
	    fraction,
	    Complex
	  } = _ref;
	  /**
	   * Calculates the power of x to y, `x ^ y`.
	   *
	   * Matrix exponentiation is supported for square matrices `x` and integers `y`:
	   * when `y` is nonnegative, `x` may be any square matrix; and when `y` is
	   * negative, `x` must be invertible, and then this function returns
	   * inv(x)^(-y).
	   *
	   * For cubic roots of negative numbers, the function returns the principal
	   * root by default. In order to let the function return the real root,
	   * math.js can be configured with `math.config({predictable: true})`.
	   * To retrieve all cubic roots of a value, use `math.cbrt(x, true)`.
	   *
	   * Syntax:
	   *
	   *    math.pow(x, y)
	   *
	   * Examples:
	   *
	   *    math.pow(2, 3)               // returns number 8
	   *
	   *    const a = math.complex(2, 3)
	   *    math.pow(a, 2)                // returns Complex -5 + 12i
	   *
	   *    const b = [[1, 2], [4, 3]]
	   *    math.pow(b, 2)               // returns Array [[9, 8], [16, 17]]
	   *
	   *    const c = [[1, 2], [4, 3]]
	   *    math.pow(c, -1)               // returns Array [[-0.6, 0.4], [0.8, -0.2]]
	   *
	   * See also:
	   *
	   *    multiply, sqrt, cbrt, nthRoot
	   *
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} x  The base
	   * @param  {number | BigNumber | bigint | Complex} y                          The exponent
	   * @return {number | BigNumber | bigint | Complex | Array | Matrix} The value of `x` to the power `y`
	   */
	  return typed(name, {
	    'number, number': _pow,
	    'Complex, Complex': function (x, y) {
	      return x.pow(y);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      if (y.isInteger() || x >= 0 || config.predictable) {
	        return x.pow(y);
	      } else {
	        return new Complex(x.toNumber(), 0).pow(y.toNumber(), 0);
	      }
	    },
	    'bigint, bigint': (x, y) => x ** y,
	    'Fraction, Fraction': function (x, y) {
	      const result = x.pow(y);
	      if (result != null) {
	        return result;
	      }
	      if (config.predictable) {
	        throw new Error('Result of pow is non-rational and cannot be expressed as a fraction');
	      } else {
	        return _pow(x.valueOf(), y.valueOf());
	      }
	    },
	    'Array, number': _powArray,
	    'Array, BigNumber': function (x, y) {
	      return _powArray(x, y.toNumber());
	    },
	    'Matrix, number': _powMatrix,
	    'Matrix, BigNumber': function (x, y) {
	      return _powMatrix(x, y.toNumber());
	    },
	    'Unit, number | BigNumber': function (x, y) {
	      return x.pow(y);
	    }
	  });

	  /**
	   * Calculates the power of x to y, x^y, for two numbers.
	   * @param {number} x
	   * @param {number} y
	   * @return {number | Complex} res
	   * @private
	   */
	  function _pow(x, y) {
	    // Alternatively could define a 'realmode' config option or something, but
	    // 'predictable' will work for now
	    if (config.predictable && !(0, _number.isInteger)(y) && x < 0) {
	      // Check to see if y can be represented as a fraction
	      try {
	        const yFrac = fraction(y);
	        const yNum = number(yFrac);
	        if (y === yNum || Math.abs((y - yNum) / y) < 1e-14) {
	          if (yFrac.d % 2n === 1n) {
	            return (yFrac.n % 2n === 0n ? 1 : -1) * Math.pow(-x, y);
	          }
	        }
	      } catch (ex) {
	        // fraction() throws an error if y is Infinity, etc.
	      }

	      // Unable to express y as a fraction, so continue on
	    }

	    // **for predictable mode** x^Infinity === NaN if x < -1
	    // N.B. this behavour is different from `Math.pow` which gives
	    // (-2)^Infinity === Infinity
	    if (config.predictable && (x < -1 && y === Infinity || x > -1 && x < 0 && y === -Infinity)) {
	      return NaN;
	    }
	    if ((0, _number.isInteger)(y) || x >= 0 || config.predictable) {
	      return (0, _index.powNumber)(x, y);
	    } else {
	      // TODO: the following infinity checks are duplicated from powNumber. Deduplicate this somehow

	      // x^Infinity === 0 if -1 < x < 1
	      // A real number 0 is returned instead of complex(0)
	      if (x * x < 1 && y === Infinity || x * x > 1 && y === -Infinity) {
	        return 0;
	      }
	      return new Complex(x, 0).pow(y, 0);
	    }
	  }

	  /**
	   * Calculate the power of a 2d array
	   * @param {Array} x     must be a 2 dimensional, square matrix
	   * @param {number} y    a integer value (positive if `x` is not invertible)
	   * @returns {Array}
	   * @private
	   */
	  function _powArray(x, y) {
	    if (!(0, _number.isInteger)(y)) {
	      throw new TypeError('For A^b, b must be an integer (value is ' + y + ')');
	    }
	    // verify that A is a 2 dimensional square matrix
	    const s = (0, _array.arraySize)(x);
	    if (s.length !== 2) {
	      throw new Error('For A^b, A must be 2 dimensional (A has ' + s.length + ' dimensions)');
	    }
	    if (s[0] !== s[1]) {
	      throw new Error('For A^b, A must be square (size is ' + s[0] + 'x' + s[1] + ')');
	    }
	    if (y < 0) {
	      try {
	        return _powArray(inv(x), -y);
	      } catch (error) {
	        if (error.message === 'Cannot calculate inverse, determinant is zero') {
	          throw new TypeError('For A^b, when A is not invertible, b must be a positive integer (value is ' + y + ')');
	        }
	        throw error;
	      }
	    }
	    let res = identity(s[0]).valueOf();
	    let px = x;
	    while (y >= 1) {
	      if ((y & 1) === 1) {
	        res = multiply(px, res);
	      }
	      y >>= 1;
	      px = multiply(px, px);
	    }
	    return res;
	  }

	  /**
	   * Calculate the power of a 2d matrix
	   * @param {Matrix} x     must be a 2 dimensional, square matrix
	   * @param {number} y    a positive, integer value
	   * @returns {Matrix}
	   * @private
	   */
	  function _powMatrix(x, y) {
	    return matrix(_powArray(x.valueOf(), y));
	  }
	});
	return pow$1;
}

var round$1 = {};

var hasRequiredRound$1;

function requireRound$1 () {
	if (hasRequiredRound$1) return round$1;
	hasRequiredRound$1 = 1;

	Object.defineProperty(round$1, "__esModule", {
	  value: true
	});
	round$1.createRound = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _number = requireNumber$3();
	var _nearlyEqual = requireNearlyEqual();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _index = requireNumber$2();
	const NO_INT = 'Number of decimals in function round must be an integer';
	const name = 'round';
	const dependencies = ['typed', 'config', 'matrix', 'equalScalar', 'zeros', 'BigNumber', 'DenseMatrix'];
	round$1.createRound = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    equalScalar,
	    zeros,
	    BigNumber,
	    DenseMatrix
	  } = _ref;
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  function toExponent(epsilon) {
	    return Math.abs((0, _number.splitNumber)(epsilon).exponent);
	  }

	  /**
	   * Round a value towards the nearest rounded value.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.round(x)
	   *    math.round(x, n)
	   *    math.round(unit, valuelessUnit)
	   *    math.round(unit, n, valuelessUnit)
	   *
	   * Examples:
	   *
	   *    math.round(3.22)             // returns number 3
	   *    math.round(3.82)             // returns number 4
	   *    math.round(-4.2)             // returns number -4
	   *    math.round(-4.7)             // returns number -5
	   *    math.round(3.22, 1)          // returns number 3.2
	   *    math.round(3.88, 1)          // returns number 3.9
	   *    math.round(-4.21, 1)         // returns number -4.2
	   *    math.round(-4.71, 1)         // returns number -4.7
	   *    math.round(math.pi, 3)       // returns number 3.142
	   *    math.round(123.45678, 2)     // returns number 123.46
	   *
	   *    const c = math.complex(3.2, -2.7)
	   *    math.round(c)                // returns Complex 3 - 3i
	   *
	   *    const unit = math.unit('3.241 cm')
	   *    const cm = math.unit('cm')
	   *    const mm = math.unit('mm')
	   *    math.round(unit, 1, cm)      // returns Unit 3.2 cm
	   *    math.round(unit, 1, mm)      // returns Unit 32.4 mm
	   *
	   *    math.round([3.2, 3.8, -4.7]) // returns Array [3, 4, -5]
	   *
	   * See also:
	   *
	   *    ceil, fix, floor
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x  Value to be rounded
	   * @param  {number | BigNumber | Array} [n=0]                            Number of decimals
	   * @param  {Unit} [valuelessUnit]                                        A valueless unit
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} Rounded value
	   */
	  return typed(name, {
	    number: function (x) {
	      // Handle round off errors by first rounding to relTol precision
	      const xEpsilon = (0, _index.roundNumber)(x, toExponent(config.relTol));
	      const xSelected = (0, _number.nearlyEqual)(x, xEpsilon, config.relTol, config.absTol) ? xEpsilon : x;
	      return (0, _index.roundNumber)(xSelected);
	    },
	    'number, number': function (x, n) {
	      // Same as number: unless user specifies more decimals than relTol
	      const epsilonExponent = toExponent(config.relTol);
	      if (n >= epsilonExponent) {
	        return (0, _index.roundNumber)(x, n);
	      }
	      const xEpsilon = (0, _index.roundNumber)(x, epsilonExponent);
	      const xSelected = (0, _number.nearlyEqual)(x, xEpsilon, config.relTol, config.absTol) ? xEpsilon : x;
	      return (0, _index.roundNumber)(xSelected, n);
	    },
	    'number, BigNumber': function (x, n) {
	      if (!n.isInteger()) {
	        throw new TypeError(NO_INT);
	      }
	      return new BigNumber(x).toDecimalPlaces(n.toNumber());
	    },
	    Complex: function (x) {
	      return x.round();
	    },
	    'Complex, number': function (x, n) {
	      if (n % 1) {
	        throw new TypeError(NO_INT);
	      }
	      return x.round(n);
	    },
	    'Complex, BigNumber': function (x, n) {
	      if (!n.isInteger()) {
	        throw new TypeError(NO_INT);
	      }
	      const _n = n.toNumber();
	      return x.round(_n);
	    },
	    BigNumber: function (x) {
	      // Handle round off errors by first rounding to relTol precision
	      const xEpsilon = new BigNumber(x).toDecimalPlaces(toExponent(config.relTol));
	      const xSelected = (0, _nearlyEqual.nearlyEqual)(x, xEpsilon, config.relTol, config.absTol) ? xEpsilon : x;
	      return xSelected.toDecimalPlaces(0);
	    },
	    'BigNumber, BigNumber': function (x, n) {
	      if (!n.isInteger()) {
	        throw new TypeError(NO_INT);
	      }

	      // Same as BigNumber: unless user specifies more decimals than relTol
	      const epsilonExponent = toExponent(config.relTol);
	      if (n >= epsilonExponent) {
	        return x.toDecimalPlaces(n.toNumber());
	      }
	      const xEpsilon = x.toDecimalPlaces(epsilonExponent);
	      const xSelected = (0, _nearlyEqual.nearlyEqual)(x, xEpsilon, config.relTol, config.absTol) ? xEpsilon : x;
	      return xSelected.toDecimalPlaces(n.toNumber());
	    },
	    // bigints can't be rounded
	    bigint: b => b,
	    'bigint, number': (b, _dummy) => b,
	    'bigint, BigNumber': (b, _dummy) => b,
	    Fraction: function (x) {
	      return x.round();
	    },
	    'Fraction, number': function (x, n) {
	      if (n % 1) {
	        throw new TypeError(NO_INT);
	      }
	      return x.round(n);
	    },
	    'Fraction, BigNumber': function (x, n) {
	      if (!n.isInteger()) {
	        throw new TypeError(NO_INT);
	      }
	      return x.round(n.toNumber());
	    },
	    'Unit, number, Unit': typed.referToSelf(self => function (x, n, unit) {
	      const valueless = x.toNumeric(unit);
	      return unit.multiply(self(valueless, n));
	    }),
	    'Unit, BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => self(x, n.toNumber(), unit)),
	    'Array | Matrix, number | BigNumber, Unit': typed.referToSelf(self => (x, n, unit) => {
	      // deep map collection, skip zeros since round(0) = 0
	      return (0, _collection.deepMap)(x, value => self(value, n, unit), true);
	    }),
	    'Array | Matrix | Unit, Unit': typed.referToSelf(self => (x, unit) => self(x, 0, unit)),
	    'Array | Matrix': typed.referToSelf(self => x => {
	      // deep map collection, skip zeros since round(0) = 0
	      return (0, _collection.deepMap)(x, self, true);
	    }),
	    'SparseMatrix, number | BigNumber': typed.referToSelf(self => (x, n) => {
	      return matAlgo11xS0s(x, n, self, false);
	    }),
	    'DenseMatrix, number | BigNumber': typed.referToSelf(self => (x, n) => {
	      return matAlgo14xDs(x, n, self, false);
	    }),
	    'Array, number | BigNumber': typed.referToSelf(self => (x, n) => {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(x), n, self, false).valueOf();
	    }),
	    'number | Complex | BigNumber | Fraction, SparseMatrix': typed.referToSelf(self => (x, n) => {
	      // check scalar is zero
	      if (equalScalar(x, 0)) {
	        // do not execute algorithm, result will be a zero matrix
	        return zeros(n.size(), n.storage());
	      }
	      return matAlgo12xSfs(n, x, self, true);
	    }),
	    'number | Complex | BigNumber | Fraction, DenseMatrix': typed.referToSelf(self => (x, n) => {
	      // check scalar is zero
	      if (equalScalar(x, 0)) {
	        // do not execute algorithm, result will be a zero matrix
	        return zeros(n.size(), n.storage());
	      }
	      return matAlgo14xDs(n, x, self, true);
	    }),
	    'number | Complex | BigNumber | Fraction, Array': typed.referToSelf(self => (x, n) => {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(n), x, self, true).valueOf();
	    })
	  });
	});
	return round$1;
}

var log$1 = {};

var hasRequiredLog$1;

function requireLog$1 () {
	if (hasRequiredLog$1) return log$1;
	hasRequiredLog$1 = 1;

	Object.defineProperty(log$1, "__esModule", {
	  value: true
	});
	log$1.createLog = void 0;
	var _factory = requireFactory$b();
	var _bigint = requireBigint$1();
	var _index = requireNumber$2();
	const name = 'log';
	const dependencies = ['config', 'typed', 'typeOf', 'divideScalar', 'Complex'];
	const nlg16 = Math.log(16);
	log$1.createLog = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    typeOf,
	    config,
	    divideScalar,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the logarithm of a value.
	   *
	   * To avoid confusion with the matrix logarithm, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.log(x)
	   *    math.log(x, base)
	   *
	   * Examples:
	   *
	   *    math.log(3.5)                  // returns 1.252762968495368
	   *    math.exp(math.log(2.4))        // returns 2.4
	   *
	   *    math.pow(10, 4)                // returns 10000
	   *    math.log(10000, 10)            // returns 4
	   *    math.log(10000) / math.log(10) // returns 4
	   *
	   *    math.log(1024, 2)              // returns 10
	   *    math.pow(2, 10)                // returns 1024
	   *
	   * See also:
	   *
	   *    exp, log2, log10, log1p
	   *
	   * History:
	   *
	   *    v0.0.2  Created
	   *    v0.2    Add optional base argument
	   *    v0.3    Handle Array input
	   *    v0.5    Handle Matrix input
	   *    v0.16   Handle BigNumber input
	   *    v0.21   Support negative BigNumbers
	   *    v11     Drop Array/Matrix support in favor of explicit map of
	   *            the scalar log function, to avoid confusion with the log
	   *            of a matrix
	   *    v14     Allow value and base to be Fractions, when the log is rational
	   *
	   * @param {number | BigNumber | Fraction | Complex} x
	   *            Value for which to calculate the logarithm.
	   * @param {number | BigNumber | Fraction | Complex} [base=e]
	   *            Optional base for the logarithm. If not provided, the natural
	   *            logarithm of `x` is calculated.
	   * @return {number | BigNumber | Fraction | Complex}
	   *            Returns the logarithm of `x`
	   */
	  function complexLog(c) {
	    return c.log();
	  }
	  function complexLogNumber(x) {
	    return complexLog(new Complex(x, 0));
	  }
	  return typed(name, {
	    number: function (x) {
	      if (x >= 0 || config.predictable) {
	        return (0, _index.logNumber)(x);
	      } else {
	        // negative value -> complex value computation
	        return complexLogNumber(x);
	      }
	    },
	    bigint: (0, _bigint.promoteLogarithm)(nlg16, _index.logNumber, config, complexLogNumber),
	    Complex: complexLog,
	    BigNumber: function (x) {
	      if (!x.isNegative() || config.predictable) {
	        return x.ln();
	      } else {
	        // downgrade to number, return Complex valued result
	        return complexLogNumber(x.toNumber());
	      }
	    },
	    'any, any': typed.referToSelf(self => (x, base) => {
	      // calculate logarithm for a specified base, log(x, base)

	      if (typeOf(x) === 'Fraction' && typeOf(base) === 'Fraction') {
	        const result = x.log(base);
	        if (result !== null) {
	          return result;
	        }
	      }
	      return divideScalar(self(x), self(base));
	    })
	  });
	});
	return log$1;
}

var log1p$1 = {};

var hasRequiredLog1p$1;

function requireLog1p$1 () {
	if (hasRequiredLog1p$1) return log1p$1;
	hasRequiredLog1p$1 = 1;

	Object.defineProperty(log1p$1, "__esModule", {
	  value: true
	});
	log1p$1.createLog1p = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	var _number = requireNumber$3();
	const name = 'log1p';
	const dependencies = ['typed', 'config', 'divideScalar', 'log', 'Complex'];
	log1p$1.createLog1p = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    divideScalar,
	    log,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the logarithm of a `value+1`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.log1p(x)
	   *    math.log1p(x, base)
	   *
	   * Examples:
	   *
	   *    math.log1p(2.5)                 // returns 1.252762968495368
	   *    math.exp(math.log1p(1.4))       // returns 2.4
	   *
	   *    math.pow(10, 4)                 // returns 10000
	   *    math.log1p(9999, 10)            // returns 4
	   *    math.log1p(9999) / math.log(10) // returns 4
	   *
	   * See also:
	   *
	   *    exp, log, log2, log10
	   *
	   * @param {number | BigNumber | Complex | Array | Matrix} x
	   *            Value for which to calculate the logarithm of `x+1`.
	   * @param {number | BigNumber | Complex} [base=e]
	   *            Optional base for the logarithm. If not provided, the natural
	   *            logarithm of `x+1` is calculated.
	   * @return {number | BigNumber | Complex | Array | Matrix}
	   *            Returns the logarithm of `x+1`
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x >= -1 || config.predictable) {
	        return (0, _number.log1p)(x);
	      } else {
	        // negative value -> complex value computation
	        return _log1pComplex(new Complex(x, 0));
	      }
	    },
	    Complex: _log1pComplex,
	    BigNumber: function (x) {
	      const y = x.plus(1);
	      if (!y.isNegative() || config.predictable) {
	        return y.ln();
	      } else {
	        // downgrade to number, return Complex valued result
	        return _log1pComplex(new Complex(x.toNumber(), 0));
	      }
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self)),
	    'any, any': typed.referToSelf(self => (x, base) => {
	      // calculate logarithm for a specified base, log1p(x, base)
	      return divideScalar(self(x), log(base));
	    })
	  });

	  /**
	   * Calculate the natural logarithm of a complex number + 1
	   * @param {Complex} x
	   * @returns {Complex}
	   * @private
	   */
	  function _log1pComplex(x) {
	    const xRe1p = x.re + 1;
	    return new Complex(Math.log(Math.sqrt(xRe1p * xRe1p + x.im * x.im)), Math.atan2(x.im, xRe1p));
	  }
	});
	return log1p$1;
}

var nthRoots$1 = {};

var hasRequiredNthRoots$1;

function requireNthRoots$1 () {
	if (hasRequiredNthRoots$1) return nthRoots$1;
	hasRequiredNthRoots$1 = 1;

	Object.defineProperty(nthRoots$1, "__esModule", {
	  value: true
	});
	nthRoots$1.createNthRoots = void 0;
	var _factory = requireFactory$b();
	const name = 'nthRoots';
	const dependencies = ['config', 'typed', 'divideScalar', 'Complex'];
	nthRoots$1.createNthRoots = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    divideScalar,
	    Complex
	  } = _ref;
	  /**
	   * Each function here returns a real multiple of i as a Complex value.
	   * @param  {number} val
	   * @return {Complex} val, i*val, -val or -i*val for index 0, 1, 2, 3
	   */
	  // This is used to fix float artifacts for zero-valued components.
	  const _calculateExactResult = [function realPos(val) {
	    return new Complex(val, 0);
	  }, function imagPos(val) {
	    return new Complex(0, val);
	  }, function realNeg(val) {
	    return new Complex(-val, 0);
	  }, function imagNeg(val) {
	    return new Complex(0, -val);
	  }];

	  /**
	   * Calculate the nth root of a Complex Number a using De Movire's Theorem.
	   * @param  {Complex} a
	   * @param  {number} root
	   * @return {Array} array of n Complex Roots
	   */
	  function _nthComplexRoots(a, root) {
	    if (root < 0) throw new Error('Root must be greater than zero');
	    if (root === 0) throw new Error('Root must be non-zero');
	    if (root % 1 !== 0) throw new Error('Root must be an integer');
	    if (a === 0 || a.abs() === 0) return [new Complex(0, 0)];
	    const aIsNumeric = typeof a === 'number';
	    let offset;
	    // determine the offset (argument of a)/(pi/2)
	    if (aIsNumeric || a.re === 0 || a.im === 0) {
	      if (aIsNumeric) {
	        offset = 2 * +(a < 0); // numeric value on the real axis
	      } else if (a.im === 0) {
	        offset = 2 * +(a.re < 0); // complex value on the real axis
	      } else {
	        offset = 2 * +(a.im < 0) + 1; // complex value on the imaginary axis
	      }
	    }
	    const arg = a.arg();
	    const abs = a.abs();
	    const roots = [];
	    const r = Math.pow(abs, 1 / root);
	    for (let k = 0; k < root; k++) {
	      const halfPiFactor = (offset + 4 * k) / root;
	      /**
	       * If (offset + 4*k)/root is an integral multiple of pi/2
	       * then we can produce a more exact result.
	       */
	      if (halfPiFactor === Math.round(halfPiFactor)) {
	        roots.push(_calculateExactResult[halfPiFactor % 4](r));
	        continue;
	      }
	      roots.push(new Complex({
	        r,
	        phi: (arg + 2 * Math.PI * k) / root
	      }));
	    }
	    return roots;
	  }

	  /**
	   * Calculate the nth roots of a value.
	   * An nth root of a positive real number A,
	   * is a positive real solution of the equation "x^root = A".
	   * This function returns an array of Complex values.
	   * Note that currently the precision of Complex numbers are limited
	   * to the precision of a 64-bit IEEE floating point, so even if the input
	   * is a BigNumber with greater precision, rounding to 64 bits will occur
	   * in computing the nth roots.
	   *
	   * Syntax:
	   *
	   *    math.nthRoots(x)
	   *    math.nthRoots(x, root)
	   *
	   * Examples:
	   *
	   *    math.nthRoots(1) // returns [ {re: 1, im: 0}, {re: -1, im: 0} ]
	   *
	   *    math.nthRoots(1, 3)
	   *    // Complex [1, "-0.5+0.866025403784439i", "-0.5-0.866025403784439i"]
	   *
	   * See also:
	   *
	   *    nthRoot, pow, sqrt
	   *
	   * History:
	   *
	   *    v1.1   Created
	   *
	   * @param {number | BigNumber | Fraction | Complex} x Number to be rounded
	   * @param {number} [root=2] Optional root, default value is 2
	   * @return {number | BigNumber | Fraction | Complex} Returns the nth roots
	   */
	  return typed(name, {
	    Complex: function (x) {
	      return _nthComplexRoots(x, 2);
	    },
	    'Complex, number': _nthComplexRoots
	  });
	});
	return nthRoots$1;
}

var dotPow$1 = {};

var hasRequiredDotPow$1;

function requireDotPow$1 () {
	if (hasRequiredDotPow$1) return dotPow$1;
	hasRequiredDotPow$1 = 1;

	Object.defineProperty(dotPow$1, "__esModule", {
	  value: true
	});
	dotPow$1.createDotPow = void 0;
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'dotPow';
	const dependencies = ['typed', 'equalScalar', 'matrix', 'pow', 'DenseMatrix', 'concat', 'SparseMatrix'];
	dotPow$1.createDotPow = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar,
	    matrix,
	    pow,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const powScalarSignatures = {};
	  for (const signature in pow.signatures) {
	    if (Object.prototype.hasOwnProperty.call(pow.signatures, signature)) {
	      if (!signature.includes('Matrix') && !signature.includes('Array')) {
	        powScalarSignatures[signature] = pow.signatures[signature];
	      }
	    }
	  }
	  const powScalar = typed(powScalarSignatures);

	  /**
	   * Calculates the power of x to y element wise.
	   *
	   * Syntax:
	   *
	   *    math.dotPow(x, y)
	   *
	   * Examples:
	   *
	   *    math.dotPow(2, 3)            // returns number 8
	   *
	   *    const a = [[1, 2], [4, 3]]
	   *    math.dotPow(a, 2)            // returns Array [[1, 4], [16, 9]]
	   *    math.pow(a, 2)               // returns Array [[9, 8], [16, 17]]
	   *
	   * See also:
	   *
	   *    pow, sqrt, multiply
	   *
	   * @param  {number | BigNumber | Complex | Unit | Array | Matrix} x  The base
	   * @param  {number | BigNumber | Complex | Unit | Array | Matrix} y  The exponent
	   * @return {number | BigNumber | Complex | Unit | Array | Matrix}                     The value of `x` to the power `y`
	   */
	  return typed(name, matrixAlgorithmSuite({
	    elop: powScalar,
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo11xS0s,
	    sS: matAlgo12xSfs
	  }));
	});
	return dotPow$1;
}

var dotDivide$1 = {};

var hasRequiredDotDivide$1;

function requireDotDivide$1 () {
	if (hasRequiredDotDivide$1) return dotDivide$1;
	hasRequiredDotDivide$1 = 1;

	Object.defineProperty(dotDivide$1, "__esModule", {
	  value: true
	});
	dotDivide$1.createDotDivide = void 0;
	var _factory = requireFactory$b();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'dotDivide';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'divideScalar', 'DenseMatrix', 'concat', 'SparseMatrix'];
	dotDivide$1.createDotDivide = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    divideScalar,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Divide two matrices element wise. The function accepts both matrices and
	   * scalar values.
	   *
	   * Syntax:
	   *
	   *    math.dotDivide(x, y)
	   *
	   * Examples:
	   *
	   *    math.dotDivide(2, 4)   // returns 0.5
	   *
	   *    a = [[9, 5], [6, 1]]
	   *    b = [[3, 2], [5, 2]]
	   *
	   *    math.dotDivide(a, b)   // returns [[3, 2.5], [1.2, 0.5]]
	   *    math.divide(a, b)      // returns [[1.75, 0.75], [-1.75, 2.25]]
	   *
	   * See also:
	   *
	   *    divide, multiply, dotMultiply
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x Numerator
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} y Denominator
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix}                    Quotient, `x ./ y`
	   */
	  return typed(name, matrixAlgorithmSuite({
	    elop: divideScalar,
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    SD: matAlgo02xDS0,
	    Ss: matAlgo11xS0s,
	    sS: matAlgo12xSfs
	  }));
	});
	return dotDivide$1;
}

var lsolve$1 = {};

var solveValidation = {};

var hasRequiredSolveValidation;

function requireSolveValidation () {
	if (hasRequiredSolveValidation) return solveValidation;
	hasRequiredSolveValidation = 1;

	Object.defineProperty(solveValidation, "__esModule", {
	  value: true
	});
	solveValidation.createSolveValidation = createSolveValidation;
	var _is = requireIs();
	var _array = requireArray();
	var _string = requireString$2();
	function createSolveValidation(_ref) {
	  let {
	    DenseMatrix
	  } = _ref;
	  /**
	   * Validates matrix and column vector b for backward/forward substitution algorithms.
	   *
	   * @param {Matrix} m            An N x N matrix
	   * @param {Array | Matrix} b    A column vector
	   * @param {Boolean} copy        Return a copy of vector b
	   *
	   * @return {DenseMatrix}        Dense column vector b
	   */
	  return function solveValidation(m, b, copy) {
	    const mSize = m.size();
	    if (mSize.length !== 2) {
	      throw new RangeError('Matrix must be two dimensional (size: ' + (0, _string.format)(mSize) + ')');
	    }
	    const rows = mSize[0];
	    const columns = mSize[1];
	    if (rows !== columns) {
	      throw new RangeError('Matrix must be square (size: ' + (0, _string.format)(mSize) + ')');
	    }
	    let data = [];
	    if ((0, _is.isMatrix)(b)) {
	      const bSize = b.size();
	      const bdata = b._data;

	      // 1-dim vector
	      if (bSize.length === 1) {
	        if (bSize[0] !== rows) {
	          throw new RangeError('Dimension mismatch. Matrix columns must match vector length.');
	        }
	        for (let i = 0; i < rows; i++) {
	          data[i] = [bdata[i]];
	        }
	        return new DenseMatrix({
	          data,
	          size: [rows, 1],
	          datatype: b._datatype
	        });
	      }

	      // 2-dim column
	      if (bSize.length === 2) {
	        if (bSize[0] !== rows || bSize[1] !== 1) {
	          throw new RangeError('Dimension mismatch. Matrix columns must match vector length.');
	        }
	        if ((0, _is.isDenseMatrix)(b)) {
	          if (copy) {
	            data = [];
	            for (let i = 0; i < rows; i++) {
	              data[i] = [bdata[i][0]];
	            }
	            return new DenseMatrix({
	              data,
	              size: [rows, 1],
	              datatype: b._datatype
	            });
	          }
	          return b;
	        }
	        if ((0, _is.isSparseMatrix)(b)) {
	          for (let i = 0; i < rows; i++) {
	            data[i] = [0];
	          }
	          const values = b._values;
	          const index = b._index;
	          const ptr = b._ptr;
	          for (let k1 = ptr[1], k = ptr[0]; k < k1; k++) {
	            const i = index[k];
	            data[i][0] = values[k];
	          }
	          return new DenseMatrix({
	            data,
	            size: [rows, 1],
	            datatype: b._datatype
	          });
	        }
	      }
	      throw new RangeError('Dimension mismatch. The right side has to be either 1- or 2-dimensional vector.');
	    }
	    if ((0, _is.isArray)(b)) {
	      const bsize = (0, _array.arraySize)(b);
	      if (bsize.length === 1) {
	        if (bsize[0] !== rows) {
	          throw new RangeError('Dimension mismatch. Matrix columns must match vector length.');
	        }
	        for (let i = 0; i < rows; i++) {
	          data[i] = [b[i]];
	        }
	        return new DenseMatrix({
	          data,
	          size: [rows, 1]
	        });
	      }
	      if (bsize.length === 2) {
	        if (bsize[0] !== rows || bsize[1] !== 1) {
	          throw new RangeError('Dimension mismatch. Matrix columns must match vector length.');
	        }
	        for (let i = 0; i < rows; i++) {
	          data[i] = [b[i][0]];
	        }
	        return new DenseMatrix({
	          data,
	          size: [rows, 1]
	        });
	      }
	      throw new RangeError('Dimension mismatch. The right side has to be either 1- or 2-dimensional vector.');
	    }
	  };
	}
	return solveValidation;
}

var hasRequiredLsolve$1;

function requireLsolve$1 () {
	if (hasRequiredLsolve$1) return lsolve$1;
	hasRequiredLsolve$1 = 1;

	Object.defineProperty(lsolve$1, "__esModule", {
	  value: true
	});
	lsolve$1.createLsolve = void 0;
	var _factory = requireFactory$b();
	var _solveValidation = requireSolveValidation();
	const name = 'lsolve';
	const dependencies = ['typed', 'matrix', 'divideScalar', 'multiplyScalar', 'subtractScalar', 'equalScalar', 'DenseMatrix'];
	lsolve$1.createLsolve = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    divideScalar,
	    multiplyScalar,
	    subtractScalar,
	    equalScalar,
	    DenseMatrix
	  } = _ref;
	  const solveValidation = (0, _solveValidation.createSolveValidation)({
	    DenseMatrix
	  });

	  /**
	   * Finds one solution of a linear equation system by forwards substitution. Matrix must be a lower triangular matrix. Throws an error if there's no solution.
	   *
	   * `L * x = b`
	   *
	   * Syntax:
	   *
	   *    math.lsolve(L, b)
	   *
	   * Examples:
	   *
	   *    const a = [[-2, 3], [2, 1]]
	   *    const b = [11, 9]
	   *    const x = lsolve(a, b)  // [[-5.5], [20]]
	   *
	   * See also:
	   *
	   *    lsolveAll, lup, slu, usolve, lusolve
	   *
	   * @param {Matrix, Array} L       A N x N matrix or array (L)
	   * @param {Matrix, Array} b       A column vector with the b values
	   *
	   * @return {DenseMatrix | Array}  A column vector with the linear system solution (x)
	   */
	  return typed(name, {
	    'SparseMatrix, Array | Matrix': function (m, b) {
	      return _sparseForwardSubstitution(m, b);
	    },
	    'DenseMatrix, Array | Matrix': function (m, b) {
	      return _denseForwardSubstitution(m, b);
	    },
	    'Array, Array | Matrix': function (a, b) {
	      const m = matrix(a);
	      const r = _denseForwardSubstitution(m, b);
	      return r.valueOf();
	    }
	  });
	  function _denseForwardSubstitution(m, b) {
	    // validate matrix and vector, return copy of column vector b
	    b = solveValidation(m, b, true);
	    const bdata = b._data;
	    const rows = m._size[0];
	    const columns = m._size[1];

	    // result
	    const x = [];
	    const mdata = m._data;

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      const bj = bdata[j][0] || 0;
	      let xj;
	      if (!equalScalar(bj, 0)) {
	        // non-degenerate row, find solution

	        const vjj = mdata[j][j];
	        if (equalScalar(vjj, 0)) {
	          throw new Error('Linear system cannot be solved since matrix is singular');
	        }
	        xj = divideScalar(bj, vjj);

	        // loop rows
	        for (let i = j + 1; i < rows; i++) {
	          bdata[i] = [subtractScalar(bdata[i][0] || 0, multiplyScalar(xj, mdata[i][j]))];
	        }
	      } else {
	        // degenerate row, we can choose any value
	        xj = 0;
	      }
	      x[j] = [xj];
	    }
	    return new DenseMatrix({
	      data: x,
	      size: [rows, 1]
	    });
	  }
	  function _sparseForwardSubstitution(m, b) {
	    // validate matrix and vector, return copy of column vector b
	    b = solveValidation(m, b, true);
	    const bdata = b._data;
	    const rows = m._size[0];
	    const columns = m._size[1];
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;

	    // result
	    const x = [];

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      const bj = bdata[j][0] || 0;
	      if (!equalScalar(bj, 0)) {
	        // non-degenerate row, find solution

	        let vjj = 0;
	        // matrix values & indices (column j)
	        const jValues = [];
	        const jIndices = [];

	        // first and last index in the column
	        const firstIndex = ptr[j];
	        const lastIndex = ptr[j + 1];

	        // values in column, find value at [j, j]
	        for (let k = firstIndex; k < lastIndex; k++) {
	          const i = index[k];

	          // check row (rows are not sorted!)
	          if (i === j) {
	            vjj = values[k];
	          } else if (i > j) {
	            // store lower triangular
	            jValues.push(values[k]);
	            jIndices.push(i);
	          }
	        }

	        // at this point we must have a value in vjj
	        if (equalScalar(vjj, 0)) {
	          throw new Error('Linear system cannot be solved since matrix is singular');
	        }
	        const xj = divideScalar(bj, vjj);
	        for (let k = 0, l = jIndices.length; k < l; k++) {
	          const i = jIndices[k];
	          bdata[i] = [subtractScalar(bdata[i][0] || 0, multiplyScalar(xj, jValues[k]))];
	        }
	        x[j] = [xj];
	      } else {
	        // degenerate row, we can choose any value
	        x[j] = [0];
	      }
	    }
	    return new DenseMatrix({
	      data: x,
	      size: [rows, 1]
	    });
	  }
	});
	return lsolve$1;
}

var usolve$1 = {};

var hasRequiredUsolve$1;

function requireUsolve$1 () {
	if (hasRequiredUsolve$1) return usolve$1;
	hasRequiredUsolve$1 = 1;

	Object.defineProperty(usolve$1, "__esModule", {
	  value: true
	});
	usolve$1.createUsolve = void 0;
	var _factory = requireFactory$b();
	var _solveValidation = requireSolveValidation();
	const name = 'usolve';
	const dependencies = ['typed', 'matrix', 'divideScalar', 'multiplyScalar', 'subtractScalar', 'equalScalar', 'DenseMatrix'];
	usolve$1.createUsolve = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    divideScalar,
	    multiplyScalar,
	    subtractScalar,
	    equalScalar,
	    DenseMatrix
	  } = _ref;
	  const solveValidation = (0, _solveValidation.createSolveValidation)({
	    DenseMatrix
	  });

	  /**
	   * Finds one solution of a linear equation system by backward substitution. Matrix must be an upper triangular matrix. Throws an error if there's no solution.
	   *
	   * `U * x = b`
	   *
	   * Syntax:
	   *
	   *    math.usolve(U, b)
	   *
	   * Examples:
	   *
	   *    const a = [[-2, 3], [2, 1]]
	   *    const b = [11, 9]
	   *    const x = usolve(a, b)  // [[8], [9]]
	   *
	   * See also:
	   *
	   *    usolveAll, lup, slu, usolve, lusolve
	   *
	   * @param {Matrix, Array} U       A N x N matrix or array (U)
	   * @param {Matrix, Array} b       A column vector with the b values
	   *
	   * @return {DenseMatrix | Array}  A column vector with the linear system solution (x)
	   */
	  return typed(name, {
	    'SparseMatrix, Array | Matrix': function (m, b) {
	      return _sparseBackwardSubstitution(m, b);
	    },
	    'DenseMatrix, Array | Matrix': function (m, b) {
	      return _denseBackwardSubstitution(m, b);
	    },
	    'Array, Array | Matrix': function (a, b) {
	      const m = matrix(a);
	      const r = _denseBackwardSubstitution(m, b);
	      return r.valueOf();
	    }
	  });
	  function _denseBackwardSubstitution(m, b) {
	    // make b into a column vector
	    b = solveValidation(m, b, true);
	    const bdata = b._data;
	    const rows = m._size[0];
	    const columns = m._size[1];

	    // result
	    const x = [];
	    const mdata = m._data;
	    // loop columns backwards
	    for (let j = columns - 1; j >= 0; j--) {
	      // b[j]
	      const bj = bdata[j][0] || 0;
	      // x[j]
	      let xj;
	      if (!equalScalar(bj, 0)) {
	        // value at [j, j]
	        const vjj = mdata[j][j];
	        if (equalScalar(vjj, 0)) {
	          // system cannot be solved
	          throw new Error('Linear system cannot be solved since matrix is singular');
	        }
	        xj = divideScalar(bj, vjj);

	        // loop rows
	        for (let i = j - 1; i >= 0; i--) {
	          // update copy of b
	          bdata[i] = [subtractScalar(bdata[i][0] || 0, multiplyScalar(xj, mdata[i][j]))];
	        }
	      } else {
	        // zero value at j
	        xj = 0;
	      }
	      // update x
	      x[j] = [xj];
	    }
	    return new DenseMatrix({
	      data: x,
	      size: [rows, 1]
	    });
	  }
	  function _sparseBackwardSubstitution(m, b) {
	    // make b into a column vector
	    b = solveValidation(m, b, true);
	    const bdata = b._data;
	    const rows = m._size[0];
	    const columns = m._size[1];
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;

	    // result
	    const x = [];

	    // loop columns backwards
	    for (let j = columns - 1; j >= 0; j--) {
	      const bj = bdata[j][0] || 0;
	      if (!equalScalar(bj, 0)) {
	        // non-degenerate row, find solution

	        let vjj = 0;

	        // upper triangular matrix values & index (column j)
	        const jValues = [];
	        const jIndices = [];

	        // first & last indeces in column
	        const firstIndex = ptr[j];
	        const lastIndex = ptr[j + 1];

	        // values in column, find value at [j, j], loop backwards
	        for (let k = lastIndex - 1; k >= firstIndex; k--) {
	          const i = index[k];

	          // check row (rows are not sorted!)
	          if (i === j) {
	            vjj = values[k];
	          } else if (i < j) {
	            // store upper triangular
	            jValues.push(values[k]);
	            jIndices.push(i);
	          }
	        }

	        // at this point we must have a value in vjj
	        if (equalScalar(vjj, 0)) {
	          throw new Error('Linear system cannot be solved since matrix is singular');
	        }
	        const xj = divideScalar(bj, vjj);
	        for (let k = 0, lastIndex = jIndices.length; k < lastIndex; k++) {
	          const i = jIndices[k];
	          bdata[i] = [subtractScalar(bdata[i][0], multiplyScalar(xj, jValues[k]))];
	        }
	        x[j] = [xj];
	      } else {
	        // degenerate row, we can choose any value
	        x[j] = [0];
	      }
	    }
	    return new DenseMatrix({
	      data: x,
	      size: [rows, 1]
	    });
	  }
	});
	return usolve$1;
}

var lsolveAll$1 = {};

var hasRequiredLsolveAll$1;

function requireLsolveAll$1 () {
	if (hasRequiredLsolveAll$1) return lsolveAll$1;
	hasRequiredLsolveAll$1 = 1;

	Object.defineProperty(lsolveAll$1, "__esModule", {
	  value: true
	});
	lsolveAll$1.createLsolveAll = void 0;
	var _factory = requireFactory$b();
	var _solveValidation = requireSolveValidation();
	const name = 'lsolveAll';
	const dependencies = ['typed', 'matrix', 'divideScalar', 'multiplyScalar', 'subtractScalar', 'equalScalar', 'DenseMatrix'];
	lsolveAll$1.createLsolveAll = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    divideScalar,
	    multiplyScalar,
	    subtractScalar,
	    equalScalar,
	    DenseMatrix
	  } = _ref;
	  const solveValidation = (0, _solveValidation.createSolveValidation)({
	    DenseMatrix
	  });

	  /**
	   * Finds all solutions of a linear equation system by forwards substitution. Matrix must be a lower triangular matrix.
	   *
	   * `L * x = b`
	   *
	   * Syntax:
	   *
	   *    math.lsolveAll(L, b)
	   *
	   * Examples:
	   *
	   *    const a = [[-2, 3], [2, 1]]
	   *    const b = [11, 9]
	   *    const x = lsolveAll(a, b)  // [ [[-5.5], [20]] ]
	   *
	   * See also:
	   *
	   *    lsolve, lup, slu, usolve, lusolve
	   *
	   * @param {Matrix, Array} L       A N x N matrix or array (L)
	   * @param {Matrix, Array} b       A column vector with the b values
	   *
	   * @return {DenseMatrix[] | Array[]}  An array of affine-independent column vectors (x) that solve the linear system
	   */
	  return typed(name, {
	    'SparseMatrix, Array | Matrix': function (m, b) {
	      return _sparseForwardSubstitution(m, b);
	    },
	    'DenseMatrix, Array | Matrix': function (m, b) {
	      return _denseForwardSubstitution(m, b);
	    },
	    'Array, Array | Matrix': function (a, b) {
	      const m = matrix(a);
	      const R = _denseForwardSubstitution(m, b);
	      return R.map(r => r.valueOf());
	    }
	  });
	  function _denseForwardSubstitution(m, b_) {
	    // the algorithm is derived from
	    // https://www.overleaf.com/read/csvgqdxggyjv

	    // array of right-hand sides
	    const B = [solveValidation(m, b_, true)._data.map(e => e[0])];
	    const M = m._data;
	    const rows = m._size[0];
	    const columns = m._size[1];

	    // loop columns
	    for (let i = 0; i < columns; i++) {
	      let L = B.length;

	      // loop right-hand sides
	      for (let k = 0; k < L; k++) {
	        const b = B[k];
	        if (!equalScalar(M[i][i], 0)) {
	          // non-singular row

	          b[i] = divideScalar(b[i], M[i][i]);
	          for (let j = i + 1; j < columns; j++) {
	            // b[j] -= b[i] * M[j,i]
	            b[j] = subtractScalar(b[j], multiplyScalar(b[i], M[j][i]));
	          }
	        } else if (!equalScalar(b[i], 0)) {
	          // singular row, nonzero RHS

	          if (k === 0) {
	            // There is no valid solution
	            return [];
	          } else {
	            // This RHS is invalid but other solutions may still exist
	            B.splice(k, 1);
	            k -= 1;
	            L -= 1;
	          }
	        } else if (k === 0) {
	          // singular row, RHS is zero

	          const bNew = [...b];
	          bNew[i] = 1;
	          for (let j = i + 1; j < columns; j++) {
	            bNew[j] = subtractScalar(bNew[j], M[j][i]);
	          }
	          B.push(bNew);
	        }
	      }
	    }
	    return B.map(x => new DenseMatrix({
	      data: x.map(e => [e]),
	      size: [rows, 1]
	    }));
	  }
	  function _sparseForwardSubstitution(m, b_) {
	    // array of right-hand sides
	    const B = [solveValidation(m, b_, true)._data.map(e => e[0])];
	    const rows = m._size[0];
	    const columns = m._size[1];
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;

	    // loop columns
	    for (let i = 0; i < columns; i++) {
	      let L = B.length;

	      // loop right-hand sides
	      for (let k = 0; k < L; k++) {
	        const b = B[k];

	        // values & indices (column i)
	        const iValues = [];
	        const iIndices = [];

	        // first & last indeces in column
	        const firstIndex = ptr[i];
	        const lastIndex = ptr[i + 1];

	        // find the value at [i, i]
	        let Mii = 0;
	        for (let j = firstIndex; j < lastIndex; j++) {
	          const J = index[j];
	          // check row
	          if (J === i) {
	            Mii = values[j];
	          } else if (J > i) {
	            // store lower triangular
	            iValues.push(values[j]);
	            iIndices.push(J);
	          }
	        }
	        if (!equalScalar(Mii, 0)) {
	          // non-singular row

	          b[i] = divideScalar(b[i], Mii);
	          for (let j = 0, lastIndex = iIndices.length; j < lastIndex; j++) {
	            const J = iIndices[j];
	            b[J] = subtractScalar(b[J], multiplyScalar(b[i], iValues[j]));
	          }
	        } else if (!equalScalar(b[i], 0)) {
	          // singular row, nonzero RHS

	          if (k === 0) {
	            // There is no valid solution
	            return [];
	          } else {
	            // This RHS is invalid but other solutions may still exist
	            B.splice(k, 1);
	            k -= 1;
	            L -= 1;
	          }
	        } else if (k === 0) {
	          // singular row, RHS is zero

	          const bNew = [...b];
	          bNew[i] = 1;
	          for (let j = 0, lastIndex = iIndices.length; j < lastIndex; j++) {
	            const J = iIndices[j];
	            bNew[J] = subtractScalar(bNew[J], iValues[j]);
	          }
	          B.push(bNew);
	        }
	      }
	    }
	    return B.map(x => new DenseMatrix({
	      data: x.map(e => [e]),
	      size: [rows, 1]
	    }));
	  }
	});
	return lsolveAll$1;
}

var usolveAll$1 = {};

var hasRequiredUsolveAll$1;

function requireUsolveAll$1 () {
	if (hasRequiredUsolveAll$1) return usolveAll$1;
	hasRequiredUsolveAll$1 = 1;

	Object.defineProperty(usolveAll$1, "__esModule", {
	  value: true
	});
	usolveAll$1.createUsolveAll = void 0;
	var _factory = requireFactory$b();
	var _solveValidation = requireSolveValidation();
	const name = 'usolveAll';
	const dependencies = ['typed', 'matrix', 'divideScalar', 'multiplyScalar', 'subtractScalar', 'equalScalar', 'DenseMatrix'];
	usolveAll$1.createUsolveAll = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    divideScalar,
	    multiplyScalar,
	    subtractScalar,
	    equalScalar,
	    DenseMatrix
	  } = _ref;
	  const solveValidation = (0, _solveValidation.createSolveValidation)({
	    DenseMatrix
	  });

	  /**
	   * Finds all solutions of a linear equation system by backward substitution. Matrix must be an upper triangular matrix.
	   *
	   * `U * x = b`
	   *
	   * Syntax:
	   *
	   *    math.usolveAll(U, b)
	   *
	   * Examples:
	   *
	   *    const a = [[-2, 3], [2, 1]]
	   *    const b = [11, 9]
	   *    const x = usolveAll(a, b)  // [ [[8], [9]] ]
	   *
	   * See also:
	   *
	   *    usolve, lup, slu, usolve, lusolve
	   *
	   * @param {Matrix, Array} U       A N x N matrix or array (U)
	   * @param {Matrix, Array} b       A column vector with the b values
	   *
	   * @return {DenseMatrix[] | Array[]}  An array of affine-independent column vectors (x) that solve the linear system
	   */
	  return typed(name, {
	    'SparseMatrix, Array | Matrix': function (m, b) {
	      return _sparseBackwardSubstitution(m, b);
	    },
	    'DenseMatrix, Array | Matrix': function (m, b) {
	      return _denseBackwardSubstitution(m, b);
	    },
	    'Array, Array | Matrix': function (a, b) {
	      const m = matrix(a);
	      const R = _denseBackwardSubstitution(m, b);
	      return R.map(r => r.valueOf());
	    }
	  });
	  function _denseBackwardSubstitution(m, b_) {
	    // the algorithm is derived from
	    // https://www.overleaf.com/read/csvgqdxggyjv

	    // array of right-hand sides
	    const B = [solveValidation(m, b_, true)._data.map(e => e[0])];
	    const M = m._data;
	    const rows = m._size[0];
	    const columns = m._size[1];

	    // loop columns backwards
	    for (let i = columns - 1; i >= 0; i--) {
	      let L = B.length;

	      // loop right-hand sides
	      for (let k = 0; k < L; k++) {
	        const b = B[k];
	        if (!equalScalar(M[i][i], 0)) {
	          // non-singular row

	          b[i] = divideScalar(b[i], M[i][i]);
	          for (let j = i - 1; j >= 0; j--) {
	            // b[j] -= b[i] * M[j,i]
	            b[j] = subtractScalar(b[j], multiplyScalar(b[i], M[j][i]));
	          }
	        } else if (!equalScalar(b[i], 0)) {
	          // singular row, nonzero RHS

	          if (k === 0) {
	            // There is no valid solution
	            return [];
	          } else {
	            // This RHS is invalid but other solutions may still exist
	            B.splice(k, 1);
	            k -= 1;
	            L -= 1;
	          }
	        } else if (k === 0) {
	          // singular row, RHS is zero

	          const bNew = [...b];
	          bNew[i] = 1;
	          for (let j = i - 1; j >= 0; j--) {
	            bNew[j] = subtractScalar(bNew[j], M[j][i]);
	          }
	          B.push(bNew);
	        }
	      }
	    }
	    return B.map(x => new DenseMatrix({
	      data: x.map(e => [e]),
	      size: [rows, 1]
	    }));
	  }
	  function _sparseBackwardSubstitution(m, b_) {
	    // array of right-hand sides
	    const B = [solveValidation(m, b_, true)._data.map(e => e[0])];
	    const rows = m._size[0];
	    const columns = m._size[1];
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;

	    // loop columns backwards
	    for (let i = columns - 1; i >= 0; i--) {
	      let L = B.length;

	      // loop right-hand sides
	      for (let k = 0; k < L; k++) {
	        const b = B[k];

	        // values & indices (column i)
	        const iValues = [];
	        const iIndices = [];

	        // first & last indeces in column
	        const firstIndex = ptr[i];
	        const lastIndex = ptr[i + 1];

	        // find the value at [i, i]
	        let Mii = 0;
	        for (let j = lastIndex - 1; j >= firstIndex; j--) {
	          const J = index[j];
	          // check row
	          if (J === i) {
	            Mii = values[j];
	          } else if (J < i) {
	            // store upper triangular
	            iValues.push(values[j]);
	            iIndices.push(J);
	          }
	        }
	        if (!equalScalar(Mii, 0)) {
	          // non-singular row

	          b[i] = divideScalar(b[i], Mii);

	          // loop upper triangular
	          for (let j = 0, lastIndex = iIndices.length; j < lastIndex; j++) {
	            const J = iIndices[j];
	            b[J] = subtractScalar(b[J], multiplyScalar(b[i], iValues[j]));
	          }
	        } else if (!equalScalar(b[i], 0)) {
	          // singular row, nonzero RHS

	          if (k === 0) {
	            // There is no valid solution
	            return [];
	          } else {
	            // This RHS is invalid but other solutions may still exist
	            B.splice(k, 1);
	            k -= 1;
	            L -= 1;
	          }
	        } else if (k === 0) {
	          // singular row, RHS is zero

	          const bNew = [...b];
	          bNew[i] = 1;

	          // loop upper triangular
	          for (let j = 0, lastIndex = iIndices.length; j < lastIndex; j++) {
	            const J = iIndices[j];
	            bNew[J] = subtractScalar(bNew[J], iValues[j]);
	          }
	          B.push(bNew);
	        }
	      }
	    }
	    return B.map(x => new DenseMatrix({
	      data: x.map(e => [e]),
	      size: [rows, 1]
	    }));
	  }
	});
	return usolveAll$1;
}

var leftShift$1 = {};

var matAlgo08xS0Sid = {};

var hasRequiredMatAlgo08xS0Sid;

function requireMatAlgo08xS0Sid () {
	if (hasRequiredMatAlgo08xS0Sid) return matAlgo08xS0Sid;
	hasRequiredMatAlgo08xS0Sid = 1;

	Object.defineProperty(matAlgo08xS0Sid, "__esModule", {
	  value: true
	});
	matAlgo08xS0Sid.createMatAlgo08xS0Sid = void 0;
	var _factory = requireFactory$b();
	var _DimensionError = requireDimensionError();
	const name = 'matAlgo08xS0Sid';
	const dependencies = ['typed', 'equalScalar'];
	matAlgo08xS0Sid.createMatAlgo08xS0Sid = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equalScalar
	  } = _ref;
	  /**
	   * Iterates over SparseMatrix A and SparseMatrix B nonzero items and invokes the callback function f(Aij, Bij).
	   * Callback function invoked MAX(NNZA, NNZB) times
	   *
	   *
	   *          ┌  f(Aij, Bij)  ; A(i,j) !== 0 && B(i,j) !== 0
	   * C(i,j) = ┤  A(i,j)       ; A(i,j) !== 0 && B(i,j) === 0
	   *          └  0            ; otherwise
	   *
	   *
	   * @param {Matrix}   a                 The SparseMatrix instance (A)
	   * @param {Matrix}   b                 The SparseMatrix instance (B)
	   * @param {Function} callback          The f(Aij,Bij) operation to invoke
	   *
	   * @return {Matrix}                    SparseMatrix (C)
	   *
	   * see https://github.com/josdejong/mathjs/pull/346#issuecomment-97620294
	   */
	  return function matAlgo08xS0Sid(a, b, callback) {
	    // sparse matrix arrays
	    const avalues = a._values;
	    const aindex = a._index;
	    const aptr = a._ptr;
	    const asize = a._size;
	    const adt = a._datatype || a._data === undefined ? a._datatype : a.getDataType();
	    // sparse matrix arrays
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    const bsize = b._size;
	    const bdt = b._datatype || b._data === undefined ? b._datatype : b.getDataType();

	    // validate dimensions
	    if (asize.length !== bsize.length) {
	      throw new _DimensionError.DimensionError(asize.length, bsize.length);
	    }

	    // check rows & columns
	    if (asize[0] !== bsize[0] || asize[1] !== bsize[1]) {
	      throw new RangeError('Dimension mismatch. Matrix A (' + asize + ') must match Matrix B (' + bsize + ')');
	    }

	    // sparse matrix cannot be a Pattern matrix
	    if (!avalues || !bvalues) {
	      throw new Error('Cannot perform operation on Pattern Sparse Matrices');
	    }

	    // rows & columns
	    const rows = asize[0];
	    const columns = asize[1];

	    // datatype
	    let dt;
	    // equal signature to use
	    let eq = equalScalar;
	    // zero value
	    let zero = 0;
	    // callback signature to use
	    let cf = callback;

	    // process data types
	    if (typeof adt === 'string' && adt === bdt && adt !== 'mixed') {
	      // datatype
	      dt = adt;
	      // find signature that matches (dt, dt)
	      eq = typed.find(equalScalar, [dt, dt]);
	      // convert 0 to the same datatype
	      zero = typed.convert(0, dt);
	      // callback
	      cf = typed.find(callback, [dt, dt]);
	    }

	    // result arrays
	    const cvalues = [];
	    const cindex = [];
	    const cptr = [];

	    // workspace
	    const x = [];
	    // marks indicating we have a value in x for a given column
	    const w = [];

	    // vars
	    let k, k0, k1, i;

	    // loop columns
	    for (let j = 0; j < columns; j++) {
	      // update cptr
	      cptr[j] = cindex.length;
	      // columns mark
	      const mark = j + 1;
	      // loop values in a
	      for (k0 = aptr[j], k1 = aptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        i = aindex[k];
	        // mark workspace
	        w[i] = mark;
	        // set value
	        x[i] = avalues[k];
	        // add index
	        cindex.push(i);
	      }
	      // loop values in b
	      for (k0 = bptr[j], k1 = bptr[j + 1], k = k0; k < k1; k++) {
	        // row
	        i = bindex[k];
	        // check value exists in workspace
	        if (w[i] === mark) {
	          // evaluate callback
	          x[i] = cf(x[i], bvalues[k]);
	        }
	      }
	      // initialize first index in j
	      k = cptr[j];
	      // loop index in j
	      while (k < cindex.length) {
	        // row
	        i = cindex[k];
	        // value @ i
	        const v = x[i];
	        // check for zero value
	        if (!eq(v, zero)) {
	          // push value
	          cvalues.push(v);
	          // increment pointer
	          k++;
	        } else {
	          // remove value @ i, do not increment pointer
	          cindex.splice(k, 1);
	        }
	      }
	    }
	    // update cptr
	    cptr[columns] = cindex.length;

	    // return sparse matrix
	    return a.createSparseMatrix({
	      values: cvalues,
	      index: cindex,
	      ptr: cptr,
	      size: [rows, columns],
	      datatype: adt === a._datatype && bdt === b._datatype ? dt : undefined
	    });
	  };
	});
	return matAlgo08xS0Sid;
}

var useMatrixForArrayScalar = {};

var hasRequiredUseMatrixForArrayScalar;

function requireUseMatrixForArrayScalar () {
	if (hasRequiredUseMatrixForArrayScalar) return useMatrixForArrayScalar;
	hasRequiredUseMatrixForArrayScalar = 1;

	Object.defineProperty(useMatrixForArrayScalar, "__esModule", {
	  value: true
	});
	useMatrixForArrayScalar.createUseMatrixForArrayScalar = void 0;
	var _factory = requireFactory$b();
	useMatrixForArrayScalar.createUseMatrixForArrayScalar = /* #__PURE__ */(0, _factory.factory)('useMatrixForArrayScalar', ['typed', 'matrix'], _ref => {
	  let {
	    typed,
	    matrix
	  } = _ref;
	  return {
	    'Array, number': typed.referTo('DenseMatrix, number', selfDn => (x, y) => selfDn(matrix(x), y).valueOf()),
	    'Array, BigNumber': typed.referTo('DenseMatrix, BigNumber', selfDB => (x, y) => selfDB(matrix(x), y).valueOf()),
	    'number, Array': typed.referTo('number, DenseMatrix', selfnD => (x, y) => selfnD(x, matrix(y)).valueOf()),
	    'BigNumber, Array': typed.referTo('BigNumber, DenseMatrix', selfBD => (x, y) => selfBD(x, matrix(y)).valueOf())
	  };
	});
	return useMatrixForArrayScalar;
}

var hasRequiredLeftShift$1;

function requireLeftShift$1 () {
	if (hasRequiredLeftShift$1) return leftShift$1;
	hasRequiredLeftShift$1 = 1;

	Object.defineProperty(leftShift$1, "__esModule", {
	  value: true
	});
	leftShift$1.createLeftShift = void 0;
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matAlgo08xS0Sid = requireMatAlgo08xS0Sid();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _useMatrixForArrayScalar = requireUseMatrixForArrayScalar();
	var _index = requireNumber$2();
	var _bitwise = requireBitwise();
	const name = 'leftShift';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'zeros', 'DenseMatrix', 'concat'];
	leftShift$1.createLeftShift = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo08xS0Sid = (0, _matAlgo08xS0Sid.createMatAlgo08xS0Sid)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const useMatrixForArrayScalar = (0, _useMatrixForArrayScalar.createUseMatrixForArrayScalar)({
	    typed,
	    matrix
	  });

	  /**
	   * Bitwise left logical shift of a value x by y number of bits, `x << y`.
	   * For matrices, the function is evaluated element wise.
	   * For units, the function is evaluated on the best prefix base.
	   *
	   * Syntax:
	   *
	   *    math.leftShift(x, y)
	   *
	   * Examples:
	   *
	   *    math.leftShift(1, 2)               // returns number 4
	   *
	   *    math.leftShift([1, 2, 4], 4)       // returns Array [16, 32, 64]
	   *
	   * See also:
	   *
	   *    leftShift, bitNot, bitOr, bitXor, rightArithShift, rightLogShift
	   *
	   * @param  {number | BigNumber | bigint | Array | Matrix} x Value to be shifted
	   * @param  {number | BigNumber | bigint} y Amount of shifts
	   * @return {number | BigNumber | bigint | Array | Matrix} `x` shifted left `y` times
	   */
	  return typed(name, {
	    'number, number': _index.leftShiftNumber,
	    'BigNumber, BigNumber': _bitwise.leftShiftBigNumber,
	    'bigint, bigint': (x, y) => x << y,
	    'SparseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(y, 0)) {
	        return x.clone();
	      }
	      return matAlgo11xS0s(x, y, self, false);
	    }),
	    'DenseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(y, 0)) {
	        return x.clone();
	      }
	      return matAlgo14xDs(x, y, self, false);
	    }),
	    'number | BigNumber, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(x, 0)) {
	        return zeros(y.size(), y.storage());
	      }
	      return matAlgo10xSids(y, x, self, true);
	    }),
	    'number | BigNumber, DenseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(x, 0)) {
	        return zeros(y.size(), y.storage());
	      }
	      return matAlgo14xDs(y, x, self, true);
	    })
	  }, useMatrixForArrayScalar, matrixAlgorithmSuite({
	    SS: matAlgo08xS0Sid,
	    DS: matAlgo01xDSid,
	    SD: matAlgo02xDS0
	  }));
	});
	return leftShift$1;
}

var rightArithShift$1 = {};

var hasRequiredRightArithShift$1;

function requireRightArithShift$1 () {
	if (hasRequiredRightArithShift$1) return rightArithShift$1;
	hasRequiredRightArithShift$1 = 1;

	Object.defineProperty(rightArithShift$1, "__esModule", {
	  value: true
	});
	rightArithShift$1.createRightArithShift = void 0;
	var _bitwise = requireBitwise();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matAlgo08xS0Sid = requireMatAlgo08xS0Sid();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _useMatrixForArrayScalar = requireUseMatrixForArrayScalar();
	var _index = requireNumber$2();
	const name = 'rightArithShift';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'zeros', 'DenseMatrix', 'concat'];
	rightArithShift$1.createRightArithShift = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo08xS0Sid = (0, _matAlgo08xS0Sid.createMatAlgo08xS0Sid)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const useMatrixForArrayScalar = (0, _useMatrixForArrayScalar.createUseMatrixForArrayScalar)({
	    typed,
	    matrix
	  });

	  /**
	   * Bitwise right arithmetic shift of a value x by y number of bits, `x >> y`.
	   * For matrices, the function is evaluated element wise.
	   * For units, the function is evaluated on the best prefix base.
	   *
	   * Syntax:
	   *
	   *    math.rightArithShift(x, y)
	   *
	   * Examples:
	   *
	   *    math.rightArithShift(4, 2)               // returns number 1
	   *
	   *    math.rightArithShift([16, -32, 64], 4)   // returns Array [1, -2, 4]
	   *
	   * See also:
	   *
	   *    bitAnd, bitNot, bitOr, bitXor, rightArithShift, rightLogShift
	   *
	   * @param  {number | BigNumber | bigint | Array | Matrix} x Value to be shifted
	   * @param  {number | BigNumber | bigint} y Amount of shifts
	   * @return {number | BigNumber | bigint | Array | Matrix} `x` zero-filled shifted right `y` times
	   */
	  return typed(name, {
	    'number, number': _index.rightArithShiftNumber,
	    'BigNumber, BigNumber': _bitwise.rightArithShiftBigNumber,
	    'bigint, bigint': (x, y) => x >> y,
	    'SparseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(y, 0)) {
	        return x.clone();
	      }
	      return matAlgo11xS0s(x, y, self, false);
	    }),
	    'DenseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(y, 0)) {
	        return x.clone();
	      }
	      return matAlgo14xDs(x, y, self, false);
	    }),
	    'number | BigNumber, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(x, 0)) {
	        return zeros(y.size(), y.storage());
	      }
	      return matAlgo10xSids(y, x, self, true);
	    }),
	    'number | BigNumber, DenseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(x, 0)) {
	        return zeros(y.size(), y.storage());
	      }
	      return matAlgo14xDs(y, x, self, true);
	    })
	  }, useMatrixForArrayScalar, matrixAlgorithmSuite({
	    SS: matAlgo08xS0Sid,
	    DS: matAlgo01xDSid,
	    SD: matAlgo02xDS0
	  }));
	});
	return rightArithShift$1;
}

var rightLogShift$1 = {};

var hasRequiredRightLogShift$1;

function requireRightLogShift$1 () {
	if (hasRequiredRightLogShift$1) return rightLogShift$1;
	hasRequiredRightLogShift$1 = 1;

	Object.defineProperty(rightLogShift$1, "__esModule", {
	  value: true
	});
	rightLogShift$1.createRightLogShift = void 0;
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matAlgo08xS0Sid = requireMatAlgo08xS0Sid();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	var _useMatrixForArrayScalar = requireUseMatrixForArrayScalar();
	const name = 'rightLogShift';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'zeros', 'DenseMatrix', 'concat'];
	rightLogShift$1.createRightLogShift = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo08xS0Sid = (0, _matAlgo08xS0Sid.createMatAlgo08xS0Sid)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const useMatrixForArrayScalar = (0, _useMatrixForArrayScalar.createUseMatrixForArrayScalar)({
	    typed,
	    matrix
	  });

	  /**
	   * Bitwise right logical shift of value x by y number of bits, `x >>> y`.
	   * For matrices, the function is evaluated element wise.
	   * For units, the function is evaluated on the best prefix base.
	   *
	   * Syntax:
	   *
	   *    math.rightLogShift(x, y)
	   *
	   * Examples:
	   *
	   *    math.rightLogShift(4, 2)               // returns number 1
	   *
	   *    math.rightLogShift([16, 32, 64], 4)    // returns Array [1, 2, 4]
	   *
	   * See also:
	   *
	   *    bitAnd, bitNot, bitOr, bitXor, leftShift, rightLogShift
	   *
	   * @param  {number | Array | Matrix} x Value to be shifted
	   * @param  {number} y Amount of shifts
	   * @return {number | Array | Matrix} `x` zero-filled shifted right `y` times
	   */

	  return typed(name, {
	    'number, number': _index.rightLogShiftNumber,
	    // 'BigNumber, BigNumber': ..., // TODO: implement BigNumber support for rightLogShift

	    'SparseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(y, 0)) {
	        return x.clone();
	      }
	      return matAlgo11xS0s(x, y, self, false);
	    }),
	    'DenseMatrix, number | BigNumber': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(y, 0)) {
	        return x.clone();
	      }
	      return matAlgo14xDs(x, y, self, false);
	    }),
	    'number | BigNumber, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(x, 0)) {
	        return zeros(y.size(), y.storage());
	      }
	      return matAlgo10xSids(y, x, self, true);
	    }),
	    'number | BigNumber, DenseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (equalScalar(x, 0)) {
	        return zeros(y.size(), y.storage());
	      }
	      return matAlgo14xDs(y, x, self, true);
	    })
	  }, useMatrixForArrayScalar, matrixAlgorithmSuite({
	    SS: matAlgo08xS0Sid,
	    DS: matAlgo01xDSid,
	    SD: matAlgo02xDS0
	  }));
	});
	return rightLogShift$1;
}

var and$1 = {};

var hasRequiredAnd$1;

function requireAnd$1 () {
	if (hasRequiredAnd$1) return and$1;
	hasRequiredAnd$1 = 1;

	Object.defineProperty(and$1, "__esModule", {
	  value: true
	});
	and$1.createAnd = void 0;
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	var _matAlgo06xS0S = requireMatAlgo06xS0S0();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _index = requireNumber$2();
	const name = 'and';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'zeros', 'not', 'concat'];
	and$1.createAnd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    not,
	    concat
	  } = _ref;
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo06xS0S0 = (0, _matAlgo06xS0S.createMatAlgo06xS0S0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Logical `and`. Test whether two values are both defined with a nonzero/nonempty value.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.and(x, y)
	   *
	   * Examples:
	   *
	   *    math.and(2, 4)   // returns true
	   *
	   *    a = [2, 0, 0]
	   *    b = [3, 7, 0]
	   *    c = 0
	   *
	   *    math.and(a, b)   // returns [true, false, false]
	   *    math.and(a, c)   // returns [false, false, false]
	   *
	   * See also:
	   *
	   *    not, or, xor
	   *
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} x First value to check
	   * @param  {number | BigNumber | bigint | Complex | Unit | Array | Matrix} y Second value to check
	   * @return {boolean | Array | Matrix}
	   *            Returns true when both inputs are defined with a nonzero/nonempty value.
	   */
	  return typed(name, {
	    'number, number': _index.andNumber,
	    'Complex, Complex': function (x, y) {
	      return (x.re !== 0 || x.im !== 0) && (y.re !== 0 || y.im !== 0);
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return !x.isZero() && !y.isZero() && !x.isNaN() && !y.isNaN();
	    },
	    'bigint, bigint': _index.andNumber,
	    'Unit, Unit': typed.referToSelf(self => (x, y) => self(x.value || 0, y.value || 0)),
	    'SparseMatrix, any': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (not(y)) {
	        // return zero matrix
	        return zeros(x.size(), x.storage());
	      }
	      return matAlgo11xS0s(x, y, self, false);
	    }),
	    'DenseMatrix, any': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (not(y)) {
	        // return zero matrix
	        return zeros(x.size(), x.storage());
	      }
	      return matAlgo14xDs(x, y, self, false);
	    }),
	    'any, SparseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (not(x)) {
	        // return zero matrix
	        return zeros(x.size(), x.storage());
	      }
	      return matAlgo11xS0s(y, x, self, true);
	    }),
	    'any, DenseMatrix': typed.referToSelf(self => (x, y) => {
	      // check scalar
	      if (not(x)) {
	        // return zero matrix
	        return zeros(x.size(), x.storage());
	      }
	      return matAlgo14xDs(y, x, self, true);
	    }),
	    'Array, any': typed.referToSelf(self => (x, y) => {
	      // use matrix implementation
	      return self(matrix(x), y).valueOf();
	    }),
	    'any, Array': typed.referToSelf(self => (x, y) => {
	      // use matrix implementation
	      return self(x, matrix(y)).valueOf();
	    })
	  }, matrixAlgorithmSuite({
	    SS: matAlgo06xS0S0,
	    DS: matAlgo02xDS0
	  }));
	});
	return and$1;
}

var compare$1 = {};

var hasRequiredCompare$1;

function requireCompare$1 () {
	if (hasRequiredCompare$1) return compare$1;
	hasRequiredCompare$1 = 1;

	Object.defineProperty(compare$1, "__esModule", {
	  value: true
	});
	compare$1.createCompareNumber = compare$1.createCompare = void 0;
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matAlgo05xSfSf = requireMatAlgo05xSfSf();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _compareUnits = requireCompareUnits();
	const name = 'compare';
	const dependencies = ['typed', 'config', 'matrix', 'equalScalar', 'BigNumber', 'Fraction', 'DenseMatrix', 'concat'];
	compare$1.createCompare = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    equalScalar,
	    matrix,
	    BigNumber,
	    Fraction,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo05xSfSf = (0, _matAlgo05xSfSf.createMatAlgo05xSfSf)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const compareUnits = (0, _compareUnits.createCompareUnits)({
	    typed
	  });

	  /**
	   * Compare two values. Returns 1 when x > y, -1 when x < y, and 0 when x == y.
	   *
	   * x and y are considered equal when the relative difference between x and y
	   * is smaller than the configured absTol and relTol. The function cannot be used to
	   * compare values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.compare(x, y)
	   *
	   * Examples:
	   *
	   *    math.compare(6, 1)           // returns 1
	   *    math.compare(2, 3)           // returns -1
	   *    math.compare(7, 7)           // returns 0
	   *    math.compare('10', '2')      // returns 1
	   *    math.compare('1000', '1e3')  // returns 0
	   *
	   *    const a = math.unit('5 cm')
	   *    const b = math.unit('40 mm')
	   *    math.compare(a, b)           // returns 1
	   *
	   *    math.compare(2, [1, 2, 3])   // returns [1, 0, -1]
	   *
	   * See also:
	   *
	   *    equal, unequal, smaller, smallerEq, larger, largerEq, compareNatural, compareText
	   *
	   * History:
	   *
	   *    v0.19  Created
	   *    v4     Changed to compare strings by numerical value
	   *    v13    Change to use separate relative and absolute tolerances
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Unit | string | Array | Matrix} x First value to compare
	   * @param  {number | BigNumber | bigint | Fraction | Unit | string | Array | Matrix} y Second value to compare
	   * @return {number | BigNumber | bigint | Fraction | Array | Matrix} Returns the result of the comparison:
	   *                                                          1 when x > y, -1 when x < y, and 0 when x == y.
	   */
	  return typed(name, createCompareNumber({
	    typed,
	    config
	  }), {
	    'boolean, boolean': function (x, y) {
	      return x === y ? 0 : x > y ? 1 : -1;
	    },
	    'BigNumber, BigNumber': function (x, y) {
	      return (0, _nearlyEqual.nearlyEqual)(x, y, config.relTol, config.absTol) ? new BigNumber(0) : new BigNumber(x.cmp(y));
	    },
	    'bigint, bigint': function (x, y) {
	      return x === y ? 0n : x > y ? 1n : -1n;
	    },
	    'Fraction, Fraction': function (x, y) {
	      return new Fraction(x.compare(y));
	    },
	    'Complex, Complex': function () {
	      throw new TypeError('No ordering relation is defined for complex numbers');
	    }
	  }, compareUnits, matrixAlgorithmSuite({
	    SS: matAlgo05xSfSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	const createCompareNumber = compare$1.createCompareNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config'], _ref2 => {
	  let {
	    typed,
	    config
	  } = _ref2;
	  return typed(name, {
	    'number, number': function (x, y) {
	      return (0, _number.nearlyEqual)(x, y, config.relTol, config.absTol) ? 0 : x > y ? 1 : -1;
	    }
	  });
	});
	return compare$1;
}

var compareNatural$1 = {};

/*
 * Natural Sort algorithm for Javascript - Version 0.7 - Released under MIT license
 * Author: Jim Palmer (based on chunking idea from Dave Koelle)
 */

var naturalSort;
var hasRequiredNaturalSort;

function requireNaturalSort () {
	if (hasRequiredNaturalSort) return naturalSort;
	hasRequiredNaturalSort = 1;
	/*jshint unused:false */
	naturalSort = function naturalSort (a, b) {
		var re = /(^([+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?)?$|^0x[0-9a-f]+$|\d+)/gi,
			sre = /(^[ ]*|[ ]*$)/g,
			dre = /(^([\w ]+,?[\w ]+)?[\w ]+,?[\w ]+\d+:\d+(:\d+)?[\w ]?|^\d{1,4}[\/\-]\d{1,4}[\/\-]\d{1,4}|^\w+, \w+ \d+, \d{4})/,
			hre = /^0x[0-9a-f]+$/i,
			ore = /^0/,
			i = function(s) { return naturalSort.insensitive && ('' + s).toLowerCase() || '' + s; },
			// convert all to strings strip whitespace
			x = i(a).replace(sre, '') || '',
			y = i(b).replace(sre, '') || '',
			// chunk/tokenize
			xN = x.replace(re, '\0$1\0').replace(/\0$/,'').replace(/^\0/,'').split('\0'),
			yN = y.replace(re, '\0$1\0').replace(/\0$/,'').replace(/^\0/,'').split('\0'),
			// numeric, hex or date detection
			xD = parseInt(x.match(hre), 16) || (xN.length !== 1 && x.match(dre) && Date.parse(x)),
			yD = parseInt(y.match(hre), 16) || xD && y.match(dre) && Date.parse(y) || null,
			oFxNcL, oFyNcL;
		// first try and sort Hex codes or Dates
		if (yD) {
			if ( xD < yD ) { return -1; }
			else if ( xD > yD ) { return 1; }
		}
		// natural sorting through split numeric strings and default strings
		for(var cLoc=0, numS=Math.max(xN.length, yN.length); cLoc < numS; cLoc++) {
			// find floats not starting with '0', string or 0 if not defined (Clint Priest)
			oFxNcL = !(xN[cLoc] || '').match(ore) && parseFloat(xN[cLoc]) || xN[cLoc] || 0;
			oFyNcL = !(yN[cLoc] || '').match(ore) && parseFloat(yN[cLoc]) || yN[cLoc] || 0;
			// handle numeric vs string comparison - number < string - (Kyle Adams)
			if (isNaN(oFxNcL) !== isNaN(oFyNcL)) { return (isNaN(oFxNcL)) ? 1 : -1; }
			// rely on string comparison if different types - i.e. '02' < 2 != '02' < '2'
			else if (typeof oFxNcL !== typeof oFyNcL) {
				oFxNcL += '';
				oFyNcL += '';
			}
			if (oFxNcL < oFyNcL) { return -1; }
			if (oFxNcL > oFyNcL) { return 1; }
		}
		return 0;
	};
	return naturalSort;
}

var hasRequiredCompareNatural$1;

function requireCompareNatural$1 () {
	if (hasRequiredCompareNatural$1) return compareNatural$1;
	hasRequiredCompareNatural$1 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(compareNatural$1, "__esModule", {
	  value: true
	});
	compareNatural$1.createCompareNatural = void 0;
	var _javascriptNaturalSort = _interopRequireDefault(requireNaturalSort());
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'compareNatural';
	const dependencies = ['typed', 'compare'];
	compareNatural$1.createCompareNatural = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    compare
	  } = _ref;
	  const compareBooleans = compare.signatures['boolean,boolean'];

	  /**
	   * Compare two values of any type in a deterministic, natural way.
	   *
	   * For numeric values, the function works the same as `math.compare`.
	   * For types of values that can't be compared mathematically,
	   * the function compares in a natural way.
	   *
	   * For numeric values, x and y are considered equal when the relative
	   * difference between x and y is smaller than the configured relTol and absTol.
	   * The function cannot be used to compare values smaller than
	   * approximately 2.22e-16.
	   *
	   * For Complex numbers, first the real parts are compared. If equal,
	   * the imaginary parts are compared.
	   *
	   * Strings are compared with a natural sorting algorithm, which
	   * orders strings in a "logic" way following some heuristics.
	   * This differs from the function `compare`, which converts the string
	   * into a numeric value and compares that. The function `compareText`
	   * on the other hand compares text lexically.
	   *
	   * Arrays and Matrices are compared value by value until there is an
	   * unequal pair of values encountered. Objects are compared by sorted
	   * keys until the keys or their values are unequal.
	   *
	   * Syntax:
	   *
	   *    math.compareNatural(x, y)
	   *
	   * Examples:
	   *
	   *    math.compareNatural(6, 1)              // returns 1
	   *    math.compareNatural(2, 3)              // returns -1
	   *    math.compareNatural(7, 7)              // returns 0
	   *
	   *    math.compareNatural('10', '2')         // returns 1
	   *    math.compareText('10', '2')            // returns -1
	   *    math.compare('10', '2')                // returns 1
	   *
	   *    math.compareNatural('Answer: 10', 'Answer: 2') // returns 1
	   *    math.compareText('Answer: 10', 'Answer: 2')    // returns -1
	   *    math.compare('Answer: 10', 'Answer: 2')
	   *    // Error: Cannot convert "Answer: 10" to a number
	   *
	   *    const a = math.unit('5 cm')
	   *    const b = math.unit('40 mm')
	   *    math.compareNatural(a, b)              // returns 1
	   *
	   *    const c = math.complex('2 + 3i')
	   *    const d = math.complex('2 + 4i')
	   *    math.compareNatural(c, d)              // returns -1
	   *
	   *    math.compareNatural([1, 2, 4], [1, 2, 3]) // returns 1
	   *    math.compareNatural([1, 2, 3], [1, 2])    // returns 1
	   *    math.compareNatural([1, 5], [1, 2, 3])    // returns 1
	   *    math.compareNatural([1, 2], [1, 2])       // returns 0
	   *
	   *    math.compareNatural({a: 2}, {a: 4})       // returns -1
	   *
	   * See also:
	   *
	   *    compare, compareText
	   *
	   * History:
	   *
	   *     v15.2   Handle bigints
	   *     v3.14   Created
	   *
	   *
	   * @param  {*} x First value to compare
	   * @param  {*} y Second value to compare
	   * @return {number} Returns the result of the comparison:
	   *                  1 when x > y, -1 when x < y, and 0 when x == y.
	   */
	  return typed(name, {
	    'any, any': _compareNatural
	  }); // just to check # args

	  function _compareNatural(x, y) {
	    const typeX = (0, _is.typeOf)(x);
	    const typeY = (0, _is.typeOf)(y);
	    let c;

	    // numeric types
	    if ((typeX === 'number' || typeX === 'BigNumber' || typeX === 'Fraction' || typeX === 'bigint') && (typeY === 'number' || typeY === 'BigNumber' || typeY === 'Fraction' || typeY === 'bigint')) {
	      c = compare(x, y);
	      if (c.toString() !== '0') {
	        // c can be number, BigNumber, or Fraction
	        return c > 0 ? 1 : -1; // return a number
	      } else {
	        return (0, _javascriptNaturalSort.default)(typeX, typeY);
	      }
	    }

	    // matrix types
	    const matTypes = ['Array', 'DenseMatrix', 'SparseMatrix'];
	    if (matTypes.includes(typeX) || matTypes.includes(typeY)) {
	      c = compareMatricesAndArrays(_compareNatural, x, y);
	      if (c !== 0) {
	        return c;
	      } else {
	        return (0, _javascriptNaturalSort.default)(typeX, typeY);
	      }
	    }

	    // in case of different types, order by name of type, i.e. 'BigNumber' < 'Complex'
	    if (typeX !== typeY) {
	      return (0, _javascriptNaturalSort.default)(typeX, typeY);
	    }
	    if (typeX === 'Complex') {
	      return compareComplexNumbers(x, y);
	    }
	    if (typeX === 'Unit') {
	      if (x.equalBase(y)) {
	        return _compareNatural(x.value, y.value);
	      }

	      // compare by units
	      return compareArrays(_compareNatural, x.formatUnits(), y.formatUnits());
	    }
	    if (typeX === 'boolean') {
	      return compareBooleans(x, y);
	    }
	    if (typeX === 'string') {
	      return (0, _javascriptNaturalSort.default)(x, y);
	    }
	    if (typeX === 'Object') {
	      return compareObjects(_compareNatural, x, y);
	    }
	    if (typeX === 'null') {
	      return 0;
	    }
	    if (typeX === 'undefined') {
	      return 0;
	    }

	    // this should not occur...
	    throw new TypeError('Unsupported type of value "' + typeX + '"');
	  }

	  /**
	   * Compare mixed matrix/array types, by converting to same-shaped array.
	   * This comparator is non-deterministic regarding input types.
	   * @param {Array | SparseMatrix | DenseMatrix | *} x
	   * @param {Array | SparseMatrix | DenseMatrix | *} y
	   * @returns {number} Returns the comparison result: -1, 0, or 1
	   */
	  function compareMatricesAndArrays(compareNatural, x, y) {
	    if ((0, _is.isSparseMatrix)(x) && (0, _is.isSparseMatrix)(y)) {
	      return compareArrays(compareNatural, x.toJSON().values, y.toJSON().values);
	    }
	    if ((0, _is.isSparseMatrix)(x)) {
	      // note: convert to array is expensive
	      return compareMatricesAndArrays(compareNatural, x.toArray(), y);
	    }
	    if ((0, _is.isSparseMatrix)(y)) {
	      // note: convert to array is expensive
	      return compareMatricesAndArrays(compareNatural, x, y.toArray());
	    }

	    // convert DenseArray into Array
	    if ((0, _is.isDenseMatrix)(x)) {
	      return compareMatricesAndArrays(compareNatural, x.toJSON().data, y);
	    }
	    if ((0, _is.isDenseMatrix)(y)) {
	      return compareMatricesAndArrays(compareNatural, x, y.toJSON().data);
	    }

	    // convert scalars to array
	    if (!Array.isArray(x)) {
	      return compareMatricesAndArrays(compareNatural, [x], y);
	    }
	    if (!Array.isArray(y)) {
	      return compareMatricesAndArrays(compareNatural, x, [y]);
	    }
	    return compareArrays(compareNatural, x, y);
	  }

	  /**
	   * Compare two Arrays
	   *
	   * - First, compares value by value
	   * - Next, if all corresponding values are equal,
	   *   look at the length: longest array will be considered largest
	   *
	   * @param {Array} x
	   * @param {Array} y
	   * @returns {number} Returns the comparison result: -1, 0, or 1
	   */
	  function compareArrays(compareNatural, x, y) {
	    // compare each value
	    for (let i = 0, ii = Math.min(x.length, y.length); i < ii; i++) {
	      const v = compareNatural(x[i], y[i]);
	      if (v !== 0) {
	        return v;
	      }
	    }

	    // compare the size of the arrays
	    if (x.length > y.length) {
	      return 1;
	    }
	    if (x.length < y.length) {
	      return -1;
	    }

	    // both Arrays have equal size and content
	    return 0;
	  }

	  /**
	   * Compare two objects
	   *
	   * - First, compare sorted property names
	   * - Next, compare the property values
	   *
	   * @param {Object} x
	   * @param {Object} y
	   * @returns {number} Returns the comparison result: -1, 0, or 1
	   */
	  function compareObjects(compareNatural, x, y) {
	    const keysX = Object.keys(x);
	    const keysY = Object.keys(y);

	    // compare keys
	    keysX.sort(_javascriptNaturalSort.default);
	    keysY.sort(_javascriptNaturalSort.default);
	    const c = compareArrays(compareNatural, keysX, keysY);
	    if (c !== 0) {
	      return c;
	    }

	    // compare values
	    for (let i = 0; i < keysX.length; i++) {
	      const v = compareNatural(x[keysX[i]], y[keysY[i]]);
	      if (v !== 0) {
	        return v;
	      }
	    }
	    return 0;
	  }
	});

	/**
	 * Compare two complex numbers, `x` and `y`:
	 *
	 * - First, compare the real values of `x` and `y`
	 * - If equal, compare the imaginary values of `x` and `y`
	 *
	 * @params {Complex} x
	 * @params {Complex} y
	 * @returns {number} Returns the comparison result: -1, 0, or 1
	 */
	function compareComplexNumbers(x, y) {
	  if (x.re > y.re) {
	    return 1;
	  }
	  if (x.re < y.re) {
	    return -1;
	  }
	  if (x.im > y.im) {
	    return 1;
	  }
	  if (x.im < y.im) {
	    return -1;
	  }
	  return 0;
	}
	return compareNatural$1;
}

var compareText$1 = {};

var hasRequiredCompareText$1;

function requireCompareText$1 () {
	if (hasRequiredCompareText$1) return compareText$1;
	hasRequiredCompareText$1 = 1;

	Object.defineProperty(compareText$1, "__esModule", {
	  value: true
	});
	compareText$1.createCompareTextNumber = compareText$1.createCompareText = void 0;
	var _string = requireString$2();
	var _factory = requireFactory$b();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'compareText';
	const dependencies = ['typed', 'matrix', 'concat'];
	_string.compareText.signature = 'any, any';
	compareText$1.createCompareText = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    concat
	  } = _ref;
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Compare two strings lexically. Comparison is case sensitive.
	   * Returns 1 when x > y, -1 when x < y, and 0 when x == y.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.compareText(x, y)
	   *
	   * Examples:
	   *
	   *    math.compareText('B', 'A')     // returns 1
	   *    math.compareText('2', '10')    // returns 1
	   *    math.compare('2', '10')        // returns -1
	   *    math.compareNatural('2', '10') // returns -1
	   *
	   *    math.compareText('B', ['A', 'B', 'C']) // returns [1, 0, -1]
	   *
	   * See also:
	   *
	   *    equal, equalText, compare, compareNatural
	   *
	   * @param  {string | Array | DenseMatrix} x First string to compare
	   * @param  {string | Array | DenseMatrix} y Second string to compare
	   * @return {number | Array | DenseMatrix} Returns the result of the comparison:
	   *                                        1 when x > y, -1 when x < y, and 0 when x == y.
	   */
	  return typed(name, _string.compareText, matrixAlgorithmSuite({
	    elop: _string.compareText,
	    Ds: true
	  }));
	});
	compareText$1.createCompareTextNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed'], _ref2 => {
	  let {
	    typed
	  } = _ref2;
	  return typed(name, _string.compareText);
	});
	return compareText$1;
}

var equal$2 = {};

var hasRequiredEqual$1;

function requireEqual$1 () {
	if (hasRequiredEqual$1) return equal$2;
	hasRequiredEqual$1 = 1;

	Object.defineProperty(equal$2, "__esModule", {
	  value: true
	});
	equal$2.createEqualNumber = equal$2.createEqual = void 0;
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'equal';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'DenseMatrix', 'SparseMatrix'];
	equal$2.createEqual = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix
	  });

	  /**
	   * Test whether two values are equal.
	   *
	   * The function tests whether the relative difference between x and y is
	   * smaller than the configured relTol and absTol. The function cannot be used to
	   * compare values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * In case of complex numbers, x.re must equal y.re, and x.im must equal y.im.
	   *
	   * Values `null` and `undefined` are compared strictly, thus `null` is only
	   * equal to `null` and nothing else, and `undefined` is only equal to
	   * `undefined` and nothing else. Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.equal(x, y)
	   *
	   * Examples:
	   *
	   *    math.equal(2 + 2, 3)         // returns false
	   *    math.equal(2 + 2, 4)         // returns true
	   *
	   *    const a = math.unit('50 cm')
	   *    const b = math.unit('5 m')
	   *    math.equal(a, b)             // returns false
	   *
	   *    const a = math.unit('500 cm')
	   *    const b = math.unit('5 m')
	   *    math.equal(a, b)             // returns true
	   *
	   *    const c = [2, 5, 1]
	   *    const d = [2, 7, 1]
	   *    math.equal(c, d)             // returns [true, false, true]...
	   *    math.deepEqual(c, d)         // returns false
	   *
	   *    math.equal("1000", "1e3")    // returns true
	   *    math.equal(0, null)          // returns false
	   *
	   * See also:
	   *
	   *    unequal, smaller, smallerEq, larger, largerEq, compare, deepEqual, equalText
	   *
	   * History:
	   *
	   *    v13    Handle bigints
	   *    v11.6  Support matrix broadcasting
	   *    v4     Compare strings by their numerical value
	   *    v0.24  Handle `null` and `undefined`
	   *    v0.23  Compare collections elementwise
	   *    v0.20  Compare floating-point numbers within epsilon, allowing roundoff
	   *    v0.2   Created
	   *
	   * @param  {number | BigNumber | bigint | boolean | Complex | Unit | string | Array | Matrix} x First value to compare
	   * @param  {number | BigNumber | bigint | boolean | Complex | Unit | string | Array | Matrix} y Second value to compare
	   * @return {boolean | Array | Matrix} Returns true when the compared values are equal, else returns false
	   */
	  return typed(name, createEqualNumber({
	    typed,
	    equalScalar
	  }), matrixAlgorithmSuite({
	    elop: equalScalar,
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	const createEqualNumber = equal$2.createEqualNumber = (0, _factory.factory)(name, ['typed', 'equalScalar'], _ref2 => {
	  let {
	    typed,
	    equalScalar
	  } = _ref2;
	  return typed(name, {
	    'any, any': function (x, y) {
	      // strict equality for null and undefined?
	      if (x === null) {
	        return y === null;
	      }
	      if (y === null) {
	        return x === null;
	      }
	      if (x === undefined) {
	        return y === undefined;
	      }
	      if (y === undefined) {
	        return x === undefined;
	      }
	      return equalScalar(x, y);
	    }
	  });
	});
	return equal$2;
}

var equalText$1 = {};

var hasRequiredEqualText$1;

function requireEqualText$1 () {
	if (hasRequiredEqualText$1) return equalText$1;
	hasRequiredEqualText$1 = 1;

	Object.defineProperty(equalText$1, "__esModule", {
	  value: true
	});
	equalText$1.createEqualText = void 0;
	var _factory = requireFactory$b();
	const name = 'equalText';
	const dependencies = ['typed', 'compareText', 'isZero'];
	equalText$1.createEqualText = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    compareText,
	    isZero
	  } = _ref;
	  /**
	   * Check equality of two strings. Comparison is case sensitive.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.equalText(x, y)
	   *
	   * Examples:
	   *
	   *    math.equalText('Hello', 'Hello')     // returns true
	   *    math.equalText('a', 'A')             // returns false
	   *    math.equal('2e3', '2000')            // returns true
	   *    math.equalText('2e3', '2000')        // returns false
	   *
	   *    math.equalText('B', ['A', 'B', 'C']) // returns [false, true, false]
	   *
	   * See also:
	   *
	   *    equal, compareText, compare, compareNatural
	   *
	   * @param  {string | Array | DenseMatrix} x First string to compare
	   * @param  {string | Array | DenseMatrix} y Second string to compare
	   * @return {number | Array | DenseMatrix} Returns true if the values are equal, and false if not.
	   */
	  return typed(name, {
	    'any, any': function (x, y) {
	      return isZero(compareText(x, y));
	    }
	  });
	});
	return equalText$1;
}

var smaller$1 = {};

var hasRequiredSmaller$1;

function requireSmaller$1 () {
	if (hasRequiredSmaller$1) return smaller$1;
	hasRequiredSmaller$1 = 1;

	Object.defineProperty(smaller$1, "__esModule", {
	  value: true
	});
	smaller$1.createSmallerNumber = smaller$1.createSmaller = void 0;
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _compareUnits = requireCompareUnits();
	const name = 'smaller';
	const dependencies = ['typed', 'config', 'bignumber', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	smaller$1.createSmaller = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    bignumber,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const compareUnits = (0, _compareUnits.createCompareUnits)({
	    typed
	  });

	  /**
	   * Test whether value x is smaller than y.
	   *
	   * The function returns true when x is smaller than y and the relative
	   * difference between x and y is smaller than the configured relTol and absTol. The
	   * function cannot be used to compare values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.smaller(x, y)
	   *
	   * Examples:
	   *
	   *    math.smaller(2, 3)            // returns true
	   *    math.smaller(5, 2 * 2)        // returns false
	   *
	   *    const a = math.unit('5 cm')
	   *    const b = math.unit('2 inch')
	   *    math.smaller(a, b)            // returns true
	   *
	   * See also:
	   *
	   *    equal, unequal, smallerEq, smaller, smallerEq, compare
	   *
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} x First value to compare
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} y Second value to compare
	   * @return {boolean | Array | Matrix} Returns true when the x is smaller than y, else returns false
	   */
	  function bignumSmaller(x, y) {
	    return x.lt(y) && !(0, _nearlyEqual.nearlyEqual)(x, y, config.relTol, config.absTol);
	  }
	  return typed(name, createSmallerNumber({
	    typed,
	    config
	  }), {
	    'boolean, boolean': (x, y) => x < y,
	    'BigNumber, BigNumber': bignumSmaller,
	    'bigint, bigint': (x, y) => x < y,
	    'Fraction, Fraction': (x, y) => x.compare(y) === -1,
	    'Fraction, BigNumber': function (x, y) {
	      return bignumSmaller(bignumber(x), y);
	    },
	    'BigNumber, Fraction': function (x, y) {
	      return bignumSmaller(x, bignumber(y));
	    },
	    'Complex, Complex': function (x, y) {
	      throw new TypeError('No ordering relation is defined for complex numbers');
	    }
	  }, compareUnits, matrixAlgorithmSuite({
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	const createSmallerNumber = smaller$1.createSmallerNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config'], _ref2 => {
	  let {
	    typed,
	    config
	  } = _ref2;
	  return typed(name, {
	    'number, number': function (x, y) {
	      return x < y && !(0, _number.nearlyEqual)(x, y, config.relTol, config.absTol);
	    }
	  });
	});
	return smaller$1;
}

var smallerEq$1 = {};

var hasRequiredSmallerEq$1;

function requireSmallerEq$1 () {
	if (hasRequiredSmallerEq$1) return smallerEq$1;
	hasRequiredSmallerEq$1 = 1;

	Object.defineProperty(smallerEq$1, "__esModule", {
	  value: true
	});
	smallerEq$1.createSmallerEqNumber = smallerEq$1.createSmallerEq = void 0;
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _compareUnits = requireCompareUnits();
	const name = 'smallerEq';
	const dependencies = ['typed', 'config', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	smallerEq$1.createSmallerEq = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const compareUnits = (0, _compareUnits.createCompareUnits)({
	    typed
	  });

	  /**
	   * Test whether value x is smaller or equal to y.
	   *
	   * The function returns true when x is smaller than y or the relative
	   * difference between x and y is smaller than the configured relTol and absTol. The
	   * function cannot be used to compare values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.smallerEq(x, y)
	   *
	   * Examples:
	   *
	   *    math.smaller(1 + 2, 3)        // returns false
	   *    math.smallerEq(1 + 2, 3)      // returns true
	   *
	   * See also:
	   *
	   *    equal, unequal, smaller, larger, largerEq, compare
	   *
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} x First value to compare
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} y Second value to compare
	   * @return {boolean | Array | Matrix} Returns true when the x is smaller than y, else returns false
	   */
	  return typed(name, createSmallerEqNumber({
	    typed,
	    config
	  }), {
	    'boolean, boolean': (x, y) => x <= y,
	    'BigNumber, BigNumber': function (x, y) {
	      return x.lte(y) || (0, _nearlyEqual.nearlyEqual)(x, y, config.relTol, config.absTol);
	    },
	    'bigint, bigint': (x, y) => x <= y,
	    'Fraction, Fraction': (x, y) => x.compare(y) !== 1,
	    'Complex, Complex': function () {
	      throw new TypeError('No ordering relation is defined for complex numbers');
	    }
	  }, compareUnits, matrixAlgorithmSuite({
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	const createSmallerEqNumber = smallerEq$1.createSmallerEqNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config'], _ref2 => {
	  let {
	    typed,
	    config
	  } = _ref2;
	  return typed(name, {
	    'number, number': function (x, y) {
	      return x <= y || (0, _number.nearlyEqual)(x, y, config.relTol, config.absTol);
	    }
	  });
	});
	return smallerEq$1;
}

var larger$1 = {};

var hasRequiredLarger$1;

function requireLarger$1 () {
	if (hasRequiredLarger$1) return larger$1;
	hasRequiredLarger$1 = 1;

	Object.defineProperty(larger$1, "__esModule", {
	  value: true
	});
	larger$1.createLargerNumber = larger$1.createLarger = void 0;
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _compareUnits = requireCompareUnits();
	const name = 'larger';
	const dependencies = ['typed', 'config', 'bignumber', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	larger$1.createLarger = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    bignumber,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const compareUnits = (0, _compareUnits.createCompareUnits)({
	    typed
	  });

	  /**
	   * Test whether value x is larger than y.
	   *
	   * The function returns true when x is larger than y and the relative
	   * difference between x and y is larger than the configured relTol and absTol. The
	   * function cannot be used to compare values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.larger(x, y)
	   *
	   * Examples:
	   *
	   *    math.larger(2, 3)             // returns false
	   *    math.larger(5, 2 + 2)         // returns true
	   *
	   *    const a = math.unit('5 cm')
	   *    const b = math.unit('2 inch')
	   *    math.larger(a, b)             // returns false
	   *
	   * See also:
	   *
	   *    equal, unequal, smaller, smallerEq, largerEq, compare
	   *
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} x First value to compare
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} y Second value to compare
	   * @return {boolean | Array | Matrix} Returns true when the x is larger than y, else returns false
	   */
	  function bignumLarger(x, y) {
	    return x.gt(y) && !(0, _nearlyEqual.nearlyEqual)(x, y, config.relTol, config.absTol);
	  }
	  return typed(name, createLargerNumber({
	    typed,
	    config
	  }), {
	    'boolean, boolean': (x, y) => x > y,
	    'BigNumber, BigNumber': bignumLarger,
	    'bigint, bigint': (x, y) => x > y,
	    'Fraction, Fraction': (x, y) => x.compare(y) === 1,
	    'Fraction, BigNumber': function (x, y) {
	      return bignumLarger(bignumber(x), y);
	    },
	    'BigNumber, Fraction': function (x, y) {
	      return bignumLarger(x, bignumber(y));
	    },
	    'Complex, Complex': function () {
	      throw new TypeError('No ordering relation is defined for complex numbers');
	    }
	  }, compareUnits, matrixAlgorithmSuite({
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	const createLargerNumber = larger$1.createLargerNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config'], _ref2 => {
	  let {
	    typed,
	    config
	  } = _ref2;
	  return typed(name, {
	    'number, number': function (x, y) {
	      return x > y && !(0, _number.nearlyEqual)(x, y, config.relTol, config.absTol);
	    }
	  });
	});
	return larger$1;
}

var largerEq$1 = {};

var hasRequiredLargerEq$1;

function requireLargerEq$1 () {
	if (hasRequiredLargerEq$1) return largerEq$1;
	hasRequiredLargerEq$1 = 1;

	Object.defineProperty(largerEq$1, "__esModule", {
	  value: true
	});
	largerEq$1.createLargerEqNumber = largerEq$1.createLargerEq = void 0;
	var _nearlyEqual = requireNearlyEqual();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	var _compareUnits = requireCompareUnits();
	const name = 'largerEq';
	const dependencies = ['typed', 'config', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	largerEq$1.createLargerEq = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  const compareUnits = (0, _compareUnits.createCompareUnits)({
	    typed
	  });

	  /**
	   * Test whether value x is larger or equal to y.
	   *
	   * The function returns true when x is larger than y or the relative
	   * difference between x and y is smaller than the configured relTol and absTol. The
	   * function cannot be used to compare values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.largerEq(x, y)
	   *
	   * Examples:
	   *
	   *    math.larger(2, 1 + 1)         // returns false
	   *    math.largerEq(2, 1 + 1)       // returns true
	   *
	   * See also:
	   *
	   *    equal, unequal, smaller, smallerEq, larger, compare
	   *
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} x First value to compare
	   * @param  {number | BigNumber | bigint | Fraction | boolean | Unit | string | Array | Matrix} y Second value to compare
	   * @return {boolean | Array | Matrix} Returns true when the x is larger or equal to y, else returns false
	   */
	  return typed(name, createLargerEqNumber({
	    typed,
	    config
	  }), {
	    'boolean, boolean': (x, y) => x >= y,
	    'BigNumber, BigNumber': function (x, y) {
	      return x.gte(y) || (0, _nearlyEqual.nearlyEqual)(x, y, config.relTol, config.absTol);
	    },
	    'bigint, bigint': function (x, y) {
	      return x >= y;
	    },
	    'Fraction, Fraction': (x, y) => x.compare(y) !== -1,
	    'Complex, Complex': function () {
	      throw new TypeError('No ordering relation is defined for complex numbers');
	    }
	  }, compareUnits, matrixAlgorithmSuite({
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	});
	const createLargerEqNumber = largerEq$1.createLargerEqNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config'], _ref2 => {
	  let {
	    typed,
	    config
	  } = _ref2;
	  return typed(name, {
	    'number, number': function (x, y) {
	      return x >= y || (0, _number.nearlyEqual)(x, y, config.relTol, config.absTol);
	    }
	  });
	});
	return largerEq$1;
}

var deepEqual$1 = {};

var hasRequiredDeepEqual$1;

function requireDeepEqual$1 () {
	if (hasRequiredDeepEqual$1) return deepEqual$1;
	hasRequiredDeepEqual$1 = 1;

	Object.defineProperty(deepEqual$1, "__esModule", {
	  value: true
	});
	deepEqual$1.createDeepEqual = void 0;
	var _factory = requireFactory$b();
	const name = 'deepEqual';
	const dependencies = ['typed', 'equal'];
	deepEqual$1.createDeepEqual = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    equal
	  } = _ref;
	  /**
	   * Test element wise whether two matrices are equal.
	   * The function accepts both matrices and scalar values.
	   *
	   * Strings are compared by their numerical value.
	   *
	   * Syntax:
	   *
	   *    math.deepEqual(x, y)
	   *
	   * Examples:
	   *
	   *    math.deepEqual(2, 4)   // returns false
	   *
	   *    const a = [2, 5, 1]
	   *    const b = [2, 7, 1]
	   *
	   *    math.deepEqual(a, b)   // returns false...
	   *    math.equal(a, b)       // returns [true, false, true]
	   *
	   * See also:
	   *
	   *    equal, unequal
	   *
	   * History:
	   *
	   *    v2     Handle Fractions
	   *    v0.2   Created
	   *
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} x First matrix to compare
	   * @param  {number | BigNumber | Fraction | Complex | Unit | Array | Matrix} y Second matrix to compare
	   * @return {number | BigNumber | Fraction | Complex | Unit | Array | Matrix}
	   *            Returns true when the input matrices have the same size and each of their elements is equal.
	   */
	  return typed(name, {
	    'any, any': function (x, y) {
	      return _deepEqual(x.valueOf(), y.valueOf());
	    }
	  });

	  /**
	   * Test whether two arrays have the same size and all elements are equal
	   * @param {Array | *} x
	   * @param {Array | *} y
	   * @return {boolean} Returns true if both arrays are deep equal
	   */
	  function _deepEqual(x, y) {
	    if (Array.isArray(x)) {
	      if (Array.isArray(y)) {
	        const len = x.length;
	        if (len !== y.length) {
	          return false;
	        }
	        for (let i = 0; i < len; i++) {
	          if (!_deepEqual(x[i], y[i])) {
	            return false;
	          }
	        }
	        return true;
	      } else {
	        return false;
	      }
	    } else {
	      if (Array.isArray(y)) {
	        return false;
	      } else {
	        return equal(x, y);
	      }
	    }
	  }
	});
	return deepEqual$1;
}

var unequal$1 = {};

var hasRequiredUnequal$1;

function requireUnequal$1 () {
	if (hasRequiredUnequal$1) return unequal$1;
	hasRequiredUnequal$1 = 1;

	Object.defineProperty(unequal$1, "__esModule", {
	  value: true
	});
	unequal$1.createUnequalNumber = unequal$1.createUnequal = void 0;
	var _factory = requireFactory$b();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo07xSSf = requireMatAlgo07xSSf();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'unequal';
	const dependencies = ['typed', 'config', 'equalScalar', 'matrix', 'DenseMatrix', 'concat', 'SparseMatrix'];
	unequal$1.createUnequal = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    equalScalar,
	    matrix,
	    DenseMatrix,
	    concat,
	    SparseMatrix
	  } = _ref;
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo07xSSf = (0, _matAlgo07xSSf.createMatAlgo07xSSf)({
	    typed,
	    SparseMatrix
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Test whether two values are unequal.
	   *
	   * The function tests whether the relative difference between x and y is
	   * larger than the configured relTol and absTol. The function cannot be used to compare
	   * values smaller than approximately 2.22e-16.
	   *
	   * For matrices, the function is evaluated element wise.
	   * In case of complex numbers, x.re must unequal y.re, or x.im must unequal y.im.
	   * Strings are compared by their numerical value.
	   *
	   * Values `null` and `undefined` are compared strictly, thus `null` is unequal
	   * with everything except `null`, and `undefined` is unequal with everything
	   * except `undefined`.
	   *
	   * Syntax:
	   *
	   *    math.unequal(x, y)
	   *
	   * Examples:
	   *
	   *    math.unequal(2 + 2, 3)       // returns true
	   *    math.unequal(2 + 2, 4)       // returns false
	   *
	   *    const a = math.unit('50 cm')
	   *    const b = math.unit('5 m')
	   *    math.unequal(a, b)           // returns true
	   *
	   *    const a = math.unit('500 cm')
	   *    const b = math.unit('5 m')
	   *    math.unequal(a, b)           // returns false
	   *
	   *    const c = [2, 5, 1]
	   *    const d = [2, 7, 1]
	   *    math.unequal(c, d)           // returns [false, true, false]...
	   *    math.deepEqual(c, d)         // returns false
	   *
	   *    math.unequal(0, null)        // returns true
	   *
	   * See also:
	   *
	   *    equal, deepEqual, smaller, smallerEq, larger, largerEq, compare
	   *
	   * History:
	   *
	   *    v13    Handle bigints
	   *    v11.6  Support matrix broadcasting
	   *    v4     Compare strings by their numeric values
	   *    v0.24  Handle `null` and `undefined`
	   *    v0.2   Created
	   *
	   * @param  {number | BigNumber | Fraction | boolean | Complex | Unit | string | Array | Matrix | undefined} x First value to compare
	   * @param  {number | BigNumber | Fraction | boolean | Complex | Unit | string | Array | Matrix | undefined} y Second value to compare
	   * @return {boolean | Array | Matrix} Returns true when the compared values are unequal, else returns false
	   */
	  return typed(name, createUnequalNumber({
	    typed,
	    equalScalar
	  }), matrixAlgorithmSuite({
	    elop: _unequal,
	    SS: matAlgo07xSSf,
	    DS: matAlgo03xDSf,
	    Ss: matAlgo12xSfs
	  }));
	  function _unequal(x, y) {
	    return !equalScalar(x, y);
	  }
	});
	const createUnequalNumber = unequal$1.createUnequalNumber = (0, _factory.factory)(name, ['typed', 'equalScalar'], _ref2 => {
	  let {
	    typed,
	    equalScalar
	  } = _ref2;
	  return typed(name, {
	    'any, any': function (x, y) {
	      // strict equality for null and undefined?
	      if (x === null) {
	        return y !== null;
	      }
	      if (y === null) {
	        return x !== null;
	      }
	      if (x === undefined) {
	        return y !== undefined;
	      }
	      if (y === undefined) {
	        return x !== undefined;
	      }
	      return !equalScalar(x, y);
	    }
	  });
	});
	return unequal$1;
}

var partitionSelect$1 = {};

var hasRequiredPartitionSelect$1;

function requirePartitionSelect$1 () {
	if (hasRequiredPartitionSelect$1) return partitionSelect$1;
	hasRequiredPartitionSelect$1 = 1;

	Object.defineProperty(partitionSelect$1, "__esModule", {
	  value: true
	});
	partitionSelect$1.createPartitionSelect = void 0;
	var _is = requireIs();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	const name = 'partitionSelect';
	const dependencies = ['typed', 'isNumeric', 'isNaN', 'compare'];
	partitionSelect$1.createPartitionSelect = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isNumeric,
	    isNaN: mathIsNaN,
	    compare
	  } = _ref;
	  const asc = compare;
	  const desc = (a, b) => -compare(a, b);

	  /**
	   * Partition-based selection of an array or 1D matrix.
	   * Will find the kth smallest value, and mutates the input array.
	   * Uses Quickselect.
	   *
	   * Syntax:
	   *
	   *    math.partitionSelect(x, k)
	   *    math.partitionSelect(x, k, compare)
	   *
	   * Examples:
	   *
	   *    math.partitionSelect([5, 10, 1], 2)                               // returns 10
	   *    math.partitionSelect(['C', 'B', 'A', 'D'], 1, math.compareText)   // returns 'B'
	   *
	   *    function sortByLength (a, b) {
	   *      return a.length - b.length
	   *    }
	   *    math.partitionSelect(['Langdon', 'Tom', 'Sara'], 2, sortByLength) // returns 'Langdon'
	   *
	   *    // the input array is mutated
	   *    arr = [5, 2, 1]
	   *    math.partitionSelect(arr, 0) // returns 1, arr is now: [1, 2, 5]
	   *    math.partitionSelect(arr, 1, 'desc') // returns 2, arr is now: [5, 2, 1]
	   *
	   * See also:
	   *
	   *    sort
	   *
	   * @param {Matrix | Array} x    A one dimensional matrix or array to sort
	   * @param {Number} k            The kth smallest value to be retrieved zero-based index
	   * @param {Function | 'asc' | 'desc'} [compare='asc']
	   *        An optional comparator function. The function is called as
	   *        `compare(a, b)`, and must return 1 when a > b, -1 when a < b,
	   *        and 0 when a == b.
	   * @return {*} Returns the kth lowest value.
	   */
	  return typed(name, {
	    'Array | Matrix, number': function (x, k) {
	      return _partitionSelect(x, k, asc);
	    },
	    'Array | Matrix, number, string': function (x, k, compare) {
	      if (compare === 'asc') {
	        return _partitionSelect(x, k, asc);
	      } else if (compare === 'desc') {
	        return _partitionSelect(x, k, desc);
	      } else {
	        throw new Error('Compare string must be "asc" or "desc"');
	      }
	    },
	    'Array | Matrix, number, function': _partitionSelect
	  });
	  function _partitionSelect(x, k, compare) {
	    if (!(0, _number.isInteger)(k) || k < 0) {
	      throw new Error('k must be a non-negative integer');
	    }
	    if ((0, _is.isMatrix)(x)) {
	      const size = x.size();
	      if (size.length > 1) {
	        throw new Error('Only one dimensional matrices supported');
	      }
	      return quickSelect(x.valueOf(), k, compare);
	    }
	    if (Array.isArray(x)) {
	      return quickSelect(x, k, compare);
	    }
	  }

	  /**
	   * Quickselect algorithm.
	   * Code adapted from:
	   * https://blog.teamleadnet.com/2012/07/quick-select-algorithm-find-kth-element.html
	   *
	   * @param {Array} arr
	   * @param {Number} k
	   * @param {Function} compare
	   * @private
	   */
	  function quickSelect(arr, k, compare) {
	    if (k >= arr.length) {
	      throw new Error('k out of bounds');
	    }

	    // check for NaN values since these can cause an infinite while loop
	    for (let i = 0; i < arr.length; i++) {
	      if (isNumeric(arr[i]) && mathIsNaN(arr[i])) {
	        return arr[i]; // return NaN
	      }
	    }
	    let from = 0;
	    let to = arr.length - 1;

	    // if from == to we reached the kth element
	    while (from < to) {
	      let r = from;
	      let w = to;
	      const pivot = arr[Math.floor(Math.random() * (to - from + 1)) + from];

	      // stop if the reader and writer meets
	      while (r < w) {
	        // arr[r] >= pivot
	        if (compare(arr[r], pivot) >= 0) {
	          // put the large values at the end
	          const tmp = arr[w];
	          arr[w] = arr[r];
	          arr[r] = tmp;
	          --w;
	        } else {
	          // the value is smaller than the pivot, skip
	          ++r;
	        }
	      }

	      // if we stepped up (r++) we need to step one down (arr[r] > pivot)
	      if (compare(arr[r], pivot) > 0) {
	        --r;
	      }

	      // the r pointer is on the end of the first k elements
	      if (k <= r) {
	        to = r;
	      } else {
	        from = r + 1;
	      }
	    }
	    return arr[k];
	  }
	});
	return partitionSelect$1;
}

var sort$1 = {};

var hasRequiredSort$1;

function requireSort$1 () {
	if (hasRequiredSort$1) return sort$1;
	hasRequiredSort$1 = 1;

	Object.defineProperty(sort$1, "__esModule", {
	  value: true
	});
	sort$1.createSort = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'sort';
	const dependencies = ['typed', 'matrix', 'compare', 'compareNatural'];
	sort$1.createSort = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    compare,
	    compareNatural
	  } = _ref;
	  const compareAsc = compare;
	  const compareDesc = (a, b) => -compare(a, b);

	  /**
	   * Sort the items in a matrix.
	   *
	   * Syntax:
	   *
	   *    math.sort(x)
	   *    math.sort(x, compare)
	   *
	   * Examples:
	   *
	   *    math.sort([5, 10, 1]) // returns [1, 5, 10]
	   *    math.sort(['C', 'B', 'A', 'D'], math.compareNatural)
	   *    // returns ['A', 'B', 'C', 'D']
	   *
	   *    function sortByLength (a, b) {
	   *      return a.length - b.length
	   *    }
	   *    math.sort(['Langdon', 'Tom', 'Sara'], sortByLength)
	   *    // returns ['Tom', 'Sara', 'Langdon']
	   *
	   * See also:
	   *
	   *    filter, forEach, map, compare, compareNatural
	   *
	   * @param {Matrix | Array} x    A one dimensional matrix or array to sort
	   * @param {Function | 'asc' | 'desc' | 'natural'} [compare='asc']
	   *        An optional _comparator function or name. The function is called as
	   *        `compare(a, b)`, and must return 1 when a > b, -1 when a < b,
	   *        and 0 when a == b.
	   * @return {Matrix | Array} Returns the sorted matrix.
	   */
	  return typed(name, {
	    Array: function (x) {
	      _arrayIsVector(x);
	      return x.sort(compareAsc);
	    },
	    Matrix: function (x) {
	      _matrixIsVector(x);
	      return matrix(x.toArray().sort(compareAsc), x.storage());
	    },
	    'Array, function': function (x, _comparator) {
	      _arrayIsVector(x);
	      return x.sort(_comparator);
	    },
	    'Matrix, function': function (x, _comparator) {
	      _matrixIsVector(x);
	      return matrix(x.toArray().sort(_comparator), x.storage());
	    },
	    'Array, string': function (x, order) {
	      _arrayIsVector(x);
	      return x.sort(_comparator(order));
	    },
	    'Matrix, string': function (x, order) {
	      _matrixIsVector(x);
	      return matrix(x.toArray().sort(_comparator(order)), x.storage());
	    }
	  });

	  /**
	   * Get the comparator for given order ('asc', 'desc', 'natural')
	   * @param {'asc' | 'desc' | 'natural'} order
	   * @return {Function} Returns a _comparator function
	   */
	  function _comparator(order) {
	    if (order === 'asc') {
	      return compareAsc;
	    } else if (order === 'desc') {
	      return compareDesc;
	    } else if (order === 'natural') {
	      return compareNatural;
	    } else {
	      throw new Error('String "asc", "desc", or "natural" expected');
	    }
	  }

	  /**
	   * Validate whether an array is one dimensional
	   * Throws an error when this is not the case
	   * @param {Array} array
	   * @private
	   */
	  function _arrayIsVector(array) {
	    if ((0, _array.arraySize)(array).length !== 1) {
	      throw new Error('One dimensional array expected');
	    }
	  }

	  /**
	   * Validate whether a matrix is one dimensional
	   * Throws an error when this is not the case
	   * @param {Matrix} matrix
	   * @private
	   */
	  function _matrixIsVector(matrix) {
	    if (matrix.size().length !== 1) {
	      throw new Error('One dimensional matrix expected');
	    }
	  }
	});
	return sort$1;
}

var max$2 = {};

var hasRequiredMax$1;

function requireMax$1 () {
	if (hasRequiredMax$1) return max$2;
	hasRequiredMax$1 = 1;

	Object.defineProperty(max$2, "__esModule", {
	  value: true
	});
	max$2.createMax = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'max';
	const dependencies = ['typed', 'config', 'numeric', 'larger', 'isNaN'];
	max$2.createMax = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    numeric,
	    larger,
	    isNaN: mathIsNaN
	  } = _ref;
	  /**
	   * Compute the maximum value of a matrix or a  list with values.
	   * In case of a multidimensional array, the maximum of the flattened array
	   * will be calculated. When `dim` is provided, the maximum over the selected
	   * dimension will be calculated. Parameter `dim` is zero-based.
	   *
	   * Syntax:
	   *
	   *     math.max(a, b, c, ...)
	   *     math.max(A)
	   *     math.max(A, dimension)
	   *
	   * Examples:
	   *
	   *     math.max(2, 1, 4, 3)                  // returns 4
	   *     math.max([2, 1, 4, 3])                // returns 4
	   *
	   *     // maximum over a specified dimension (zero-based)
	   *     math.max([[2, 5], [4, 3], [1, 7]], 0) // returns [4, 7]
	   *     math.max([[2, 5], [4, 3], [1, 7]], 1) // returns [5, 4, 7]
	   *
	   *     math.max(2.7, 7.1, -4.5, 2.0, 4.1)    // returns 7.1
	   *     math.min(2.7, 7.1, -4.5, 2.0, 4.1)    // returns -4.5
	   *
	   * See also:
	   *
	   *    mean, median, min, prod, std, sum, variance
	   *
	   * @param {... *} args  A single matrix or or multiple scalar values
	   * @return {*} The maximum value
	   */
	  return typed(name, {
	    // max([a, b, c, d, ...])
	    'Array | Matrix': _max,
	    // max([a, b, c, d, ...], dim)
	    'Array | Matrix, number | BigNumber': function (array, dim) {
	      return (0, _collection.reduce)(array, dim.valueOf(), _largest);
	    },
	    // max(a, b, c, d, ...)
	    '...': function (args) {
	      if ((0, _collection.containsCollections)(args)) {
	        throw new TypeError('Scalar values expected in function max');
	      }
	      return _max(args);
	    }
	  });

	  /**
	   * Return the largest of two values
	   * @param {*} x
	   * @param {*} y
	   * @returns {*} Returns x when x is largest, or y when y is largest
	   * @private
	   */
	  function _largest(x, y) {
	    try {
	      return larger(x, y) ? x : y;
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, 'max', y);
	    }
	  }

	  /**
	   * Recursively calculate the maximum value in an n-dimensional array
	   * @param {Array} array
	   * @return {number} max
	   * @private
	   */
	  function _max(array) {
	    let res;
	    (0, _collection.deepForEach)(array, function (value) {
	      try {
	        if (mathIsNaN(value)) {
	          res = value;
	        } else if (res === undefined || larger(value, res)) {
	          res = value;
	        }
	      } catch (err) {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'max', value);
	      }
	    });
	    if (res === undefined) {
	      throw new Error('Cannot calculate max of an empty array');
	    }

	    // make sure returning numeric value: parse a string into a numeric value
	    if (typeof res === 'string') {
	      res = numeric(res, (0, _number.safeNumberType)(res, config));
	    }
	    return res;
	  }
	});
	return max$2;
}

var min$2 = {};

var hasRequiredMin$1;

function requireMin$1 () {
	if (hasRequiredMin$1) return min$2;
	hasRequiredMin$1 = 1;

	Object.defineProperty(min$2, "__esModule", {
	  value: true
	});
	min$2.createMin = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'min';
	const dependencies = ['typed', 'config', 'numeric', 'smaller', 'isNaN'];
	min$2.createMin = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    numeric,
	    smaller,
	    isNaN: mathIsNaN
	  } = _ref;
	  /**
	   * Compute the minimum value of a matrix or a  list of values.
	   * In case of a multidimensional array, the minimum of the flattened array
	   * will be calculated. When `dim` is provided, the minimum over the selected
	   * dimension will be calculated. Parameter `dim` is zero-based.
	   *
	   * Syntax:
	   *
	   *     math.min(a, b, c, ...)
	   *     math.min(A)
	   *     math.min(A, dimension)
	   *
	   * Examples:
	   *
	   *     math.min(2, 1, 4, 3)                  // returns 1
	   *     math.min([2, 1, 4, 3])                // returns 1
	   *
	   *     // minimum over a specified dimension (zero-based)
	   *     math.min([[2, 5], [4, 3], [1, 7]], 0) // returns [1, 3]
	   *     math.min([[2, 5], [4, 3], [1, 7]], 1) // returns [2, 3, 1]
	   *
	   *     math.max(2.7, 7.1, -4.5, 2.0, 4.1)    // returns 7.1
	   *     math.min(2.7, 7.1, -4.5, 2.0, 4.1)    // returns -4.5
	   *
	   * See also:
	   *
	   *    mean, median, max, prod, std, sum, variance
	   *
	   * @param {... *} args  A single matrix or or multiple scalar values
	   * @return {*} The minimum value
	   */
	  return typed(name, {
	    // min([a, b, c, d, ...])
	    'Array | Matrix': _min,
	    // min([a, b, c, d, ...], dim)
	    'Array | Matrix, number | BigNumber': function (array, dim) {
	      return (0, _collection.reduce)(array, dim.valueOf(), _smallest);
	    },
	    // min(a, b, c, d, ...)
	    '...': function (args) {
	      if ((0, _collection.containsCollections)(args)) {
	        throw new TypeError('Scalar values expected in function min');
	      }
	      return _min(args);
	    }
	  });

	  /**
	   * Return the smallest of two values
	   * @param {*} x
	   * @param {*} y
	   * @returns {*} Returns x when x is smallest, or y when y is smallest
	   * @private
	   */
	  function _smallest(x, y) {
	    try {
	      return smaller(x, y) ? x : y;
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, 'min', y);
	    }
	  }

	  /**
	   * Recursively calculate the minimum value in an n-dimensional array
	   * @param {Array} array
	   * @return {number} min
	   * @private
	   */
	  function _min(array) {
	    let min;
	    (0, _collection.deepForEach)(array, function (value) {
	      try {
	        if (mathIsNaN(value)) {
	          min = value;
	        } else if (min === undefined || smaller(value, min)) {
	          min = value;
	        }
	      } catch (err) {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'min', value);
	      }
	    });
	    if (min === undefined) {
	      throw new Error('Cannot calculate min of an empty array');
	    }

	    // make sure returning numeric value: parse a string into a numeric value
	    if (typeof min === 'string') {
	      min = numeric(min, (0, _number.safeNumberType)(min, config));
	    }
	    return min;
	  }
	});
	return min$2;
}

var ImmutableDenseMatrix = {};

var hasRequiredImmutableDenseMatrix;

function requireImmutableDenseMatrix () {
	if (hasRequiredImmutableDenseMatrix) return ImmutableDenseMatrix;
	hasRequiredImmutableDenseMatrix = 1;

	Object.defineProperty(ImmutableDenseMatrix, "__esModule", {
	  value: true
	});
	ImmutableDenseMatrix.createImmutableDenseMatrixClass = void 0;
	var _is = requireIs();
	var _object = requireObject$3();
	var _factory = requireFactory$b();
	const name = 'ImmutableDenseMatrix';
	const dependencies = ['smaller', 'DenseMatrix'];
	ImmutableDenseMatrix.createImmutableDenseMatrixClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    smaller,
	    DenseMatrix
	  } = _ref;
	  function ImmutableDenseMatrix(data, datatype) {
	    if (!(this instanceof ImmutableDenseMatrix)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    if (datatype && !(0, _is.isString)(datatype)) {
	      throw new Error('Invalid datatype: ' + datatype);
	    }
	    if ((0, _is.isMatrix)(data) || (0, _is.isArray)(data)) {
	      // use DenseMatrix implementation
	      const matrix = new DenseMatrix(data, datatype);
	      // internal structures
	      this._data = matrix._data;
	      this._size = matrix._size;
	      this._datatype = matrix._datatype;
	      this._min = null;
	      this._max = null;
	    } else if (data && (0, _is.isArray)(data.data) && (0, _is.isArray)(data.size)) {
	      // initialize fields from JSON representation
	      this._data = data.data;
	      this._size = data.size;
	      this._datatype = data.datatype;
	      this._min = typeof data.min !== 'undefined' ? data.min : null;
	      this._max = typeof data.max !== 'undefined' ? data.max : null;
	    } else if (data) {
	      // unsupported type
	      throw new TypeError('Unsupported type of data (' + (0, _is.typeOf)(data) + ')');
	    } else {
	      // nothing provided
	      this._data = [];
	      this._size = [0];
	      this._datatype = datatype;
	      this._min = null;
	      this._max = null;
	    }
	  }
	  ImmutableDenseMatrix.prototype = new DenseMatrix();

	  /**
	   * Attach type information
	   */
	  ImmutableDenseMatrix.prototype.type = 'ImmutableDenseMatrix';
	  ImmutableDenseMatrix.prototype.isImmutableDenseMatrix = true;

	  /**
	   * Get a subset of the matrix, or replace a subset of the matrix.
	   *
	   * Usage:
	   *     const subset = matrix.subset(index)               // retrieve subset
	   *     const value = matrix.subset(index, replacement)   // replace subset
	   *
	   * @param {Index} index
	   * @param {Array | ImmutableDenseMatrix | *} [replacement]
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be filled with zeros.
	   */
	  ImmutableDenseMatrix.prototype.subset = function (index) {
	    switch (arguments.length) {
	      case 1:
	        {
	          // use base implementation
	          const m = DenseMatrix.prototype.subset.call(this, index);
	          // check result is a matrix
	          if ((0, _is.isMatrix)(m)) {
	            // return immutable matrix
	            return new ImmutableDenseMatrix({
	              data: m._data,
	              size: m._size,
	              datatype: m._datatype
	            });
	          }
	          return m;
	        }
	      // intentional fall through
	      case 2:
	      case 3:
	        throw new Error('Cannot invoke set subset on an Immutable Matrix instance');
	      default:
	        throw new SyntaxError('Wrong number of arguments');
	    }
	  };

	  /**
	   * Replace a single element in the matrix.
	   * @param {Number[]} index   Zero-based index
	   * @param {*} value
	   * @param {*} [defaultValue]        Default value, filled in on new entries when
	   *                                  the matrix is resized. If not provided,
	   *                                  new matrix elements will be left undefined.
	   * @return {ImmutableDenseMatrix} self
	   */
	  ImmutableDenseMatrix.prototype.set = function () {
	    throw new Error('Cannot invoke set on an Immutable Matrix instance');
	  };

	  /**
	   * Resize the matrix to the given size. Returns a copy of the matrix when
	   * `copy=true`, otherwise return the matrix itself (resize in place).
	   *
	   * @param {Number[]} size           The new size the matrix should have.
	   * @param {*} [defaultValue=0]      Default value, filled in on new entries.
	   *                                  If not provided, the matrix elements will
	   *                                  be filled with zeros.
	   * @param {boolean} [copy]          Return a resized copy of the matrix
	   *
	   * @return {Matrix}                 The resized matrix
	   */
	  ImmutableDenseMatrix.prototype.resize = function () {
	    throw new Error('Cannot invoke resize on an Immutable Matrix instance');
	  };

	  /**
	   * Disallows reshaping in favor of immutability.
	   *
	   * @throws {Error} Operation not allowed
	   */
	  ImmutableDenseMatrix.prototype.reshape = function () {
	    throw new Error('Cannot invoke reshape on an Immutable Matrix instance');
	  };

	  /**
	   * Create a clone of the matrix
	   * @return {ImmutableDenseMatrix} clone
	   */
	  ImmutableDenseMatrix.prototype.clone = function () {
	    return new ImmutableDenseMatrix({
	      data: (0, _object.clone)(this._data),
	      size: (0, _object.clone)(this._size),
	      datatype: this._datatype
	    });
	  };

	  /**
	   * Get a JSON representation of the matrix
	   * @returns {Object}
	   */
	  ImmutableDenseMatrix.prototype.toJSON = function () {
	    return {
	      mathjs: 'ImmutableDenseMatrix',
	      data: this._data,
	      size: this._size,
	      datatype: this._datatype
	    };
	  };

	  /**
	   * Generate a matrix from a JSON object
	   * @param {Object} json  An object structured like
	   *                       `{"mathjs": "ImmutableDenseMatrix", data: [], size: []}`,
	   *                       where mathjs is optional
	   * @returns {ImmutableDenseMatrix}
	   */
	  ImmutableDenseMatrix.fromJSON = function (json) {
	    return new ImmutableDenseMatrix(json);
	  };

	  /**
	   * Swap rows i and j in Matrix.
	   *
	   * @param {Number} i       Matrix row index 1
	   * @param {Number} j       Matrix row index 2
	   *
	   * @return {Matrix}        The matrix reference
	   */
	  ImmutableDenseMatrix.prototype.swapRows = function () {
	    throw new Error('Cannot invoke swapRows on an Immutable Matrix instance');
	  };

	  /**
	   * Calculate the minimum value in the set
	   * @return {Number | undefined} min
	   */
	  ImmutableDenseMatrix.prototype.min = function () {
	    // check min has been calculated before
	    if (this._min === null) {
	      // minimum
	      let m = null;
	      // compute min
	      this.forEach(function (v) {
	        if (m === null || smaller(v, m)) {
	          m = v;
	        }
	      });
	      this._min = m !== null ? m : undefined;
	    }
	    return this._min;
	  };

	  /**
	   * Calculate the maximum value in the set
	   * @return {Number | undefined} max
	   */
	  ImmutableDenseMatrix.prototype.max = function () {
	    // check max has been calculated before
	    if (this._max === null) {
	      // maximum
	      let m = null;
	      // compute max
	      this.forEach(function (v) {
	        if (m === null || smaller(m, v)) {
	          m = v;
	        }
	      });
	      this._max = m !== null ? m : undefined;
	    }
	    return this._max;
	  };
	  return ImmutableDenseMatrix;
	}, {
	  isClass: true
	});
	return ImmutableDenseMatrix;
}

var MatrixIndex = {};

var hasRequiredMatrixIndex;

function requireMatrixIndex () {
	if (hasRequiredMatrixIndex) return MatrixIndex;
	hasRequiredMatrixIndex = 1;

	Object.defineProperty(MatrixIndex, "__esModule", {
	  value: true
	});
	MatrixIndex.createIndexClass = void 0;
	var _is = requireIs();
	var _object = requireObject$3();
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	const name = 'Index';
	const dependencies = ['ImmutableDenseMatrix', 'getMatrixDataType'];
	MatrixIndex.createIndexClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    ImmutableDenseMatrix,
	    getMatrixDataType
	  } = _ref;
	  /**
	   * Create an index. An Index can store ranges and sets for multiple dimensions.
	   * Matrix.get, Matrix.set, and math.subset accept an Index as input.
	   *
	   * Usage:
	   *     const index = new Index(range1, range2, matrix1, array1, ...)
	   *
	   * Where each parameter can be any of:
	   *     A number
	   *     A string (containing a name of an object property)
	   *     An instance of Range
	   *     An Array with the Set values
	   *     An Array with Booleans
	   *     A Matrix with the Set values
	   *     A Matrix with Booleans
	   *
	   * The parameters start, end, and step must be integer numbers.
	   *
	   * @class Index
	   * @Constructor Index
	   * @param {...*} ranges
	   */
	  function Index() {
	    if (!(this instanceof Index)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    this._dimensions = [];
	    this._sourceSize = [];
	    this._isScalar = true;
	    for (let i = 0, ii = arguments.length; i < ii; i++) {
	      const arg = i < 0 || arguments.length <= i ? undefined : arguments[i];
	      const argIsArray = (0, _is.isArray)(arg);
	      const argIsMatrix = (0, _is.isMatrix)(arg);
	      const argType = typeof arg;
	      let sourceSize = null;
	      if ((0, _is.isRange)(arg)) {
	        this._dimensions.push(arg);
	        this._isScalar = false;
	      } else if (argIsArray || argIsMatrix) {
	        // create matrix
	        let m;
	        this._isScalar = false;
	        if (getMatrixDataType(arg) === 'boolean') {
	          if (argIsArray) m = _createImmutableMatrix(_booleansArrayToNumbersForIndex(arg).valueOf());
	          if (argIsMatrix) m = _createImmutableMatrix(_booleansArrayToNumbersForIndex(arg._data).valueOf());
	          sourceSize = arg.valueOf().length;
	        } else {
	          m = _createImmutableMatrix(arg.valueOf());
	        }
	        this._dimensions.push(m);
	      } else if (argType === 'number') {
	        this._dimensions.push(arg);
	      } else if (argType === 'bigint') {
	        this._dimensions.push(Number(arg));
	      } else if (argType === 'string') {
	        // object property (arguments.count should be 1)
	        this._dimensions.push(arg);
	      } else {
	        throw new TypeError('Dimension must be an Array, Matrix, number, bigint, string, or Range');
	      }
	      this._sourceSize.push(sourceSize);
	      // TODO: implement support for wildcard '*'
	    }
	  }

	  /**
	   * Attach type information
	   */
	  Index.prototype.type = 'Index';
	  Index.prototype.isIndex = true;
	  function _createImmutableMatrix(arg) {
	    // loop array elements
	    for (let i = 0, l = arg.length; i < l; i++) {
	      if (!(0, _is.isNumber)(arg[i]) || !(0, _number.isInteger)(arg[i])) {
	        throw new TypeError('Index parameters must be positive integer numbers');
	      }
	    }
	    // create matrix
	    const matrix = new ImmutableDenseMatrix();
	    matrix._data = arg;
	    matrix._size = [arg.length];
	    return matrix;
	  }

	  /**
	   * Create a clone of the index
	   * @memberof Index
	   * @return {Index} clone
	   */
	  Index.prototype.clone = function () {
	    const index = new Index();
	    index._dimensions = (0, _object.clone)(this._dimensions);
	    index._isScalar = this._isScalar;
	    index._sourceSize = this._sourceSize;
	    return index;
	  };

	  /**
	   * Create an index from an array with ranges/numbers
	   * @memberof Index
	   * @param {Array.<Array | number>} ranges
	   * @return {Index} index
	   * @private
	   */
	  Index.create = function (ranges) {
	    const index = new Index();
	    Index.apply(index, ranges);
	    return index;
	  };

	  /**
	   * Retrieve the size of the index, the number of elements for each dimension.
	   * @memberof Index
	   * @returns {number[]} size
	   */
	  Index.prototype.size = function () {
	    const size = [];
	    for (let i = 0, ii = this._dimensions.length; i < ii; i++) {
	      const d = this._dimensions[i];
	      size[i] = (0, _is.isString)(d) || (0, _is.isNumber)(d) ? 1 : d.size()[0];
	    }
	    return size;
	  };

	  /**
	   * Get the maximum value for each of the indexes ranges.
	   * @memberof Index
	   * @returns {number[]} max
	   */
	  Index.prototype.max = function () {
	    const values = [];
	    for (let i = 0, ii = this._dimensions.length; i < ii; i++) {
	      const range = this._dimensions[i];
	      values[i] = (0, _is.isString)(range) || (0, _is.isNumber)(range) ? range : range.max();
	    }
	    return values;
	  };

	  /**
	   * Get the minimum value for each of the indexes ranges.
	   * @memberof Index
	   * @returns {number[]} min
	   */
	  Index.prototype.min = function () {
	    const values = [];
	    for (let i = 0, ii = this._dimensions.length; i < ii; i++) {
	      const range = this._dimensions[i];
	      values[i] = (0, _is.isString)(range) || (0, _is.isNumber)(range) ? range : range.min();
	    }
	    return values;
	  };

	  /**
	   * Loop over each of the ranges of the index
	   * @memberof Index
	   * @param {Function} callback   Called for each range with a Range as first
	   *                              argument, the dimension as second, and the
	   *                              index object as third.
	   */
	  Index.prototype.forEach = function (callback) {
	    for (let i = 0, ii = this._dimensions.length; i < ii; i++) {
	      callback(this._dimensions[i], i, this);
	    }
	  };

	  /**
	   * Retrieve the dimension for the given index
	   * @memberof Index
	   * @param {Number} dim                  Number of the dimension
	   * @returns {Range | null} range
	   */
	  Index.prototype.dimension = function (dim) {
	    var _this$_dimensions$dim;
	    if (!(0, _is.isNumber)(dim)) {
	      return null;
	    }
	    return (_this$_dimensions$dim = this._dimensions[dim]) !== null && _this$_dimensions$dim !== void 0 ? _this$_dimensions$dim : null;
	  };

	  /**
	   * Test whether this index contains an object property
	   * @returns {boolean} Returns true if the index is an object property
	   */
	  Index.prototype.isObjectProperty = function () {
	    return this._dimensions.length === 1 && (0, _is.isString)(this._dimensions[0]);
	  };

	  /**
	   * Returns the object property name when the Index holds a single object property,
	   * else returns null
	   * @returns {string | null}
	   */
	  Index.prototype.getObjectProperty = function () {
	    return this.isObjectProperty() ? this._dimensions[0] : null;
	  };

	  /**
	   * Test whether this index contains only a single value.
	   *
	   * This is the case when the index is created with only scalar values as ranges,
	   * not for ranges resolving into a single value.
	   * @memberof Index
	   * @return {boolean} isScalar
	   */
	  Index.prototype.isScalar = function () {
	    return this._isScalar;
	  };

	  /**
	   * Expand the Index into an array.
	   * For example new Index([0,3], [2,7]) returns [[0,1,2], [2,3,4,5,6]]
	   * @memberof Index
	   * @returns {Array} array
	   */
	  Index.prototype.toArray = function () {
	    const array = [];
	    for (let i = 0, ii = this._dimensions.length; i < ii; i++) {
	      const dimension = this._dimensions[i];
	      array.push((0, _is.isString)(dimension) || (0, _is.isNumber)(dimension) ? dimension : dimension.toArray());
	    }
	    return array;
	  };

	  /**
	   * Get the primitive value of the Index, a two dimensional array.
	   * Equivalent to Index.toArray().
	   * @memberof Index
	   * @returns {Array} array
	   */
	  Index.prototype.valueOf = Index.prototype.toArray;

	  /**
	   * Get the string representation of the index, for example '[2:6]' or '[0:2:10, 4:7, [1,2,3]]'
	   * @memberof Index
	   * @returns {String} str
	   */
	  Index.prototype.toString = function () {
	    const strings = [];
	    for (let i = 0, ii = this._dimensions.length; i < ii; i++) {
	      const dimension = this._dimensions[i];
	      if ((0, _is.isString)(dimension)) {
	        strings.push(JSON.stringify(dimension));
	      } else {
	        strings.push(dimension.toString());
	      }
	    }
	    return '[' + strings.join(', ') + ']';
	  };

	  /**
	   * Get a JSON representation of the Index
	   * @memberof Index
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "Index", "ranges": [{"mathjs": "Range", start: 0, end: 10, step:1}, ...]}`
	   */
	  Index.prototype.toJSON = function () {
	    return {
	      mathjs: 'Index',
	      dimensions: this._dimensions
	    };
	  };

	  /**
	   * Instantiate an Index from a JSON object
	   * @memberof Index
	   * @param {Object} json A JSON object structured as:
	   *                     `{"mathjs": "Index", "dimensions": [{"mathjs": "Range", start: 0, end: 10, step:1}, ...]}`
	   * @return {Index}
	   */
	  Index.fromJSON = function (json) {
	    return Index.create(json.dimensions);
	  };
	  return Index;
	}, {
	  isClass: true
	});

	/**
	 * Receives an array of booleans and returns an array of Numbers for Index
	 * @param {Array} booleanArrayIndex An array of booleans
	 * @return {Array} A set of numbers ready for index
	 */
	function _booleansArrayToNumbersForIndex(booleanArrayIndex) {
	  // gets an array of booleans and returns an array of numbers
	  const indexOfNumbers = [];
	  booleanArrayIndex.forEach((bool, idx) => {
	    if (bool) {
	      indexOfNumbers.push(idx);
	    }
	  });
	  return indexOfNumbers;
	}
	return MatrixIndex;
}

var FibonacciHeap = {};

var hasRequiredFibonacciHeap;

function requireFibonacciHeap () {
	if (hasRequiredFibonacciHeap) return FibonacciHeap;
	hasRequiredFibonacciHeap = 1;

	Object.defineProperty(FibonacciHeap, "__esModule", {
	  value: true
	});
	FibonacciHeap.createFibonacciHeapClass = void 0;
	var _factory = requireFactory$b();
	const name = 'FibonacciHeap';
	const dependencies = ['smaller', 'larger'];
	FibonacciHeap.createFibonacciHeapClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    smaller,
	    larger
	  } = _ref;
	  const oneOverLogPhi = 1.0 / Math.log((1.0 + Math.sqrt(5.0)) / 2.0);

	  /**
	   * Fibonacci Heap implementation, used interally for Matrix math.
	   * @class FibonacciHeap
	   * @constructor FibonacciHeap
	   */
	  function FibonacciHeap() {
	    if (!(this instanceof FibonacciHeap)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }

	    // initialize fields
	    this._minimum = null;
	    this._size = 0;
	  }

	  /**
	   * Attach type information
	   */
	  FibonacciHeap.prototype.type = 'FibonacciHeap';
	  FibonacciHeap.prototype.isFibonacciHeap = true;

	  /**
	   * Inserts a new data element into the heap. No heap consolidation is
	   * performed at this time, the new node is simply inserted into the root
	   * list of this heap. Running time: O(1) actual.
	   * @memberof FibonacciHeap
	   */
	  FibonacciHeap.prototype.insert = function (key, value) {
	    // create node
	    const node = {
	      key,
	      value,
	      degree: 0
	    };
	    // check we have a node in the minimum
	    if (this._minimum) {
	      // minimum node
	      const minimum = this._minimum;
	      // update left & right of node
	      node.left = minimum;
	      node.right = minimum.right;
	      minimum.right = node;
	      node.right.left = node;
	      // update minimum node in heap if needed
	      if (smaller(key, minimum.key)) {
	        // node has a smaller key, use it as minimum
	        this._minimum = node;
	      }
	    } else {
	      // set left & right
	      node.left = node;
	      node.right = node;
	      // this is the first node
	      this._minimum = node;
	    }
	    // increment number of nodes in heap
	    this._size++;
	    // return node
	    return node;
	  };

	  /**
	   * Returns the number of nodes in heap. Running time: O(1) actual.
	   * @memberof FibonacciHeap
	   */
	  FibonacciHeap.prototype.size = function () {
	    return this._size;
	  };

	  /**
	   * Removes all elements from this heap.
	   * @memberof FibonacciHeap
	   */
	  FibonacciHeap.prototype.clear = function () {
	    this._minimum = null;
	    this._size = 0;
	  };

	  /**
	   * Returns true if the heap is empty, otherwise false.
	   * @memberof FibonacciHeap
	   */
	  FibonacciHeap.prototype.isEmpty = function () {
	    return this._size === 0;
	  };

	  /**
	   * Extracts the node with minimum key from heap. Amortized running
	   * time: O(log n).
	   * @memberof FibonacciHeap
	   */
	  FibonacciHeap.prototype.extractMinimum = function () {
	    // node to remove
	    const node = this._minimum;
	    // check we have a minimum
	    if (node === null) {
	      return node;
	    }
	    // current minimum
	    let minimum = this._minimum;
	    // get number of children
	    let numberOfChildren = node.degree;
	    // pointer to the first child
	    let x = node.child;
	    // for each child of node do...
	    while (numberOfChildren > 0) {
	      // store node in right side
	      const tempRight = x.right;
	      // remove x from child list
	      x.left.right = x.right;
	      x.right.left = x.left;
	      // add x to root list of heap
	      x.left = minimum;
	      x.right = minimum.right;
	      minimum.right = x;
	      x.right.left = x;
	      // set Parent[x] to null
	      x.parent = null;
	      x = tempRight;
	      numberOfChildren--;
	    }
	    // remove node from root list of heap
	    node.left.right = node.right;
	    node.right.left = node.left;
	    // update minimum
	    if (node === node.right) {
	      // empty
	      minimum = null;
	    } else {
	      // update minimum
	      minimum = node.right;
	      // we need to update the pointer to the root with minimum key
	      minimum = _findMinimumNode(minimum, this._size);
	    }
	    // decrement size of heap
	    this._size--;
	    // update minimum
	    this._minimum = minimum;
	    // return node
	    return node;
	  };

	  /**
	   * Removes a node from the heap given the reference to the node. The trees
	   * in the heap will be consolidated, if necessary. This operation may fail
	   * to remove the correct element if there are nodes with key value -Infinity.
	   * Running time: O(log n) amortized.
	   * @memberof FibonacciHeap
	   */
	  FibonacciHeap.prototype.remove = function (node) {
	    // decrease key value
	    this._minimum = _decreaseKey(this._minimum, node, -1);
	    // remove the smallest
	    this.extractMinimum();
	  };

	  /**
	   * Decreases the key value for a heap node, given the new value to take on.
	   * The structure of the heap may be changed and will not be consolidated.
	   * Running time: O(1) amortized.
	   * @memberof FibonacciHeap
	   */
	  function _decreaseKey(minimum, node, key) {
	    // set node key
	    node.key = key;
	    // get parent node
	    const parent = node.parent;
	    if (parent && smaller(node.key, parent.key)) {
	      // remove node from parent
	      _cut(minimum, node, parent);
	      // remove all nodes from parent to the root parent
	      _cascadingCut(minimum, parent);
	    }
	    // update minimum node if needed
	    if (smaller(node.key, minimum.key)) {
	      minimum = node;
	    }
	    // return minimum
	    return minimum;
	  }

	  /**
	   * The reverse of the link operation: removes node from the child list of parent.
	   * This method assumes that min is non-null. Running time: O(1).
	   * @memberof FibonacciHeap
	   */
	  function _cut(minimum, node, parent) {
	    // remove node from parent children and decrement Degree[parent]
	    node.left.right = node.right;
	    node.right.left = node.left;
	    parent.degree--;
	    // reset y.child if necessary
	    if (parent.child === node) {
	      parent.child = node.right;
	    }
	    // remove child if degree is 0
	    if (parent.degree === 0) {
	      parent.child = null;
	    }
	    // add node to root list of heap
	    node.left = minimum;
	    node.right = minimum.right;
	    minimum.right = node;
	    node.right.left = node;
	    // set parent[node] to null
	    node.parent = null;
	    // set mark[node] to false
	    node.mark = false;
	  }

	  /**
	   * Performs a cascading cut operation. This cuts node from its parent and then
	   * does the same for its parent, and so on up the tree.
	   * Running time: O(log n); O(1) excluding the recursion.
	   * @memberof FibonacciHeap
	   */
	  function _cascadingCut(minimum, node) {
	    // store parent node
	    const parent = node.parent;
	    // if there's a parent...
	    if (!parent) {
	      return;
	    }
	    // if node is unmarked, set it marked
	    if (!node.mark) {
	      node.mark = true;
	    } else {
	      // it's marked, cut it from parent
	      _cut(minimum, node, parent);
	      // cut its parent as well
	      _cascadingCut(parent);
	    }
	  }

	  /**
	   * Make the first node a child of the second one. Running time: O(1) actual.
	   * @memberof FibonacciHeap
	   */
	  const _linkNodes = function (node, parent) {
	    // remove node from root list of heap
	    node.left.right = node.right;
	    node.right.left = node.left;
	    // make node a Child of parent
	    node.parent = parent;
	    if (!parent.child) {
	      parent.child = node;
	      node.right = node;
	      node.left = node;
	    } else {
	      node.left = parent.child;
	      node.right = parent.child.right;
	      parent.child.right = node;
	      node.right.left = node;
	    }
	    // increase degree[parent]
	    parent.degree++;
	    // set mark[node] false
	    node.mark = false;
	  };
	  function _findMinimumNode(minimum, size) {
	    // to find trees of the same degree efficiently we use an array of length O(log n) in which we keep a pointer to one root of each degree
	    const arraySize = Math.floor(Math.log(size) * oneOverLogPhi) + 1;
	    // create list with initial capacity
	    const array = new Array(arraySize);
	    // find the number of root nodes.
	    let numRoots = 0;
	    let x = minimum;
	    if (x) {
	      numRoots++;
	      x = x.right;
	      while (x !== minimum) {
	        numRoots++;
	        x = x.right;
	      }
	    }
	    // vars
	    let y;
	    // For each node in root list do...
	    while (numRoots > 0) {
	      // access this node's degree..
	      let d = x.degree;
	      // get next node
	      const next = x.right;
	      // check if there is a node already in array with the same degree
	      while (true) {
	        // get node with the same degree is any
	        y = array[d];
	        if (!y) {
	          break;
	        }
	        // make one node with the same degree a child of the other, do this based on the key value.
	        if (larger(x.key, y.key)) {
	          const temp = y;
	          y = x;
	          x = temp;
	        }
	        // make y a child of x
	        _linkNodes(y, x);
	        // we have handled this degree, go to next one.
	        array[d] = null;
	        d++;
	      }
	      // save this node for later when we might encounter another of the same degree.
	      array[d] = x;
	      // move forward through list.
	      x = next;
	      numRoots--;
	    }
	    // Set min to null (effectively losing the root list) and reconstruct the root list from the array entries in array[].
	    minimum = null;
	    // loop nodes in array
	    for (let i = 0; i < arraySize; i++) {
	      // get current node
	      y = array[i];
	      if (!y) {
	        continue;
	      }
	      // check if we have a linked list
	      if (minimum) {
	        // First remove node from root list.
	        y.left.right = y.right;
	        y.right.left = y.left;
	        // now add to root list, again.
	        y.left = minimum;
	        y.right = minimum.right;
	        minimum.right = y;
	        y.right.left = y;
	        // check if this is a new min.
	        if (smaller(y.key, minimum.key)) {
	          minimum = y;
	        }
	      } else {
	        minimum = y;
	      }
	    }
	    return minimum;
	  }
	  return FibonacciHeap;
	}, {
	  isClass: true
	});
	return FibonacciHeap;
}

var Spa = {};

var hasRequiredSpa;

function requireSpa () {
	if (hasRequiredSpa) return Spa;
	hasRequiredSpa = 1;

	Object.defineProperty(Spa, "__esModule", {
	  value: true
	});
	Spa.createSpaClass = void 0;
	var _factory = requireFactory$b();
	const name = 'Spa';
	const dependencies = ['addScalar', 'equalScalar', 'FibonacciHeap'];
	Spa.createSpaClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    addScalar,
	    equalScalar,
	    FibonacciHeap
	  } = _ref;
	  /**
	   * An ordered Sparse Accumulator is a representation for a sparse vector that includes a dense array
	   * of the vector elements and an ordered list of non-zero elements.
	   */
	  function Spa() {
	    if (!(this instanceof Spa)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }

	    // allocate vector, TODO use typed arrays
	    this._values = [];
	    this._heap = new FibonacciHeap();
	  }

	  /**
	   * Attach type information
	   */
	  Spa.prototype.type = 'Spa';
	  Spa.prototype.isSpa = true;

	  /**
	   * Set the value for index i.
	   *
	   * @param {number} i                       The index
	   * @param {number | BigNumber | Complex}   The value at index i
	   */
	  Spa.prototype.set = function (i, v) {
	    // check we have a value @ i
	    if (!this._values[i]) {
	      // insert in heap
	      const node = this._heap.insert(i, v);
	      // set the value @ i
	      this._values[i] = node;
	    } else {
	      // update the value @ i
	      this._values[i].value = v;
	    }
	  };
	  Spa.prototype.get = function (i) {
	    const node = this._values[i];
	    if (node) {
	      return node.value;
	    }
	    return 0;
	  };
	  Spa.prototype.accumulate = function (i, v) {
	    // node @ i
	    let node = this._values[i];
	    if (!node) {
	      // insert in heap
	      node = this._heap.insert(i, v);
	      // initialize value
	      this._values[i] = node;
	    } else {
	      // accumulate value
	      node.value = addScalar(node.value, v);
	    }
	  };
	  Spa.prototype.forEach = function (from, to, callback) {
	    // references
	    const heap = this._heap;
	    const values = this._values;
	    // nodes
	    const nodes = [];
	    // node with minimum key, save it
	    let node = heap.extractMinimum();
	    if (node) {
	      nodes.push(node);
	    }
	    // extract nodes from heap (ordered)
	    while (node && node.key <= to) {
	      // check it is in range
	      if (node.key >= from) {
	        // check value is not zero
	        if (!equalScalar(node.value, 0)) {
	          // invoke callback
	          callback(node.key, node.value, this);
	        }
	      }
	      // extract next node, save it
	      node = heap.extractMinimum();
	      if (node) {
	        nodes.push(node);
	      }
	    }
	    // reinsert all nodes in heap
	    for (let i = 0; i < nodes.length; i++) {
	      // current node
	      const n = nodes[i];
	      // insert node in heap
	      node = heap.insert(n.key, n.value);
	      // update values
	      values[node.key] = node;
	    }
	  };
	  Spa.prototype.swap = function (i, j) {
	    // node @ i and j
	    let nodei = this._values[i];
	    let nodej = this._values[j];
	    // check we need to insert indeces
	    if (!nodei && nodej) {
	      // insert in heap
	      nodei = this._heap.insert(i, nodej.value);
	      // remove from heap
	      this._heap.remove(nodej);
	      // set values
	      this._values[i] = nodei;
	      this._values[j] = undefined;
	    } else if (nodei && !nodej) {
	      // insert in heap
	      nodej = this._heap.insert(j, nodei.value);
	      // remove from heap
	      this._heap.remove(nodei);
	      // set values
	      this._values[j] = nodej;
	      this._values[i] = undefined;
	    } else if (nodei && nodej) {
	      // swap values
	      const v = nodei.value;
	      nodei.value = nodej.value;
	      nodej.value = v;
	    }
	  };
	  return Spa;
	}, {
	  isClass: true
	});
	return Spa;
}

var Unit = {};

var _function$1 = {};

var lruQueue = {};

var hasRequiredLruQueue;

function requireLruQueue () {
	if (hasRequiredLruQueue) return lruQueue;
	hasRequiredLruQueue = 1;

	Object.defineProperty(lruQueue, "__esModule", {
	  value: true
	});
	lruQueue.lruQueue = lruQueue$1;
	// (c) 2018, Mariusz Nowak
	// SPDX-License-Identifier: ISC
	// Derived from https://github.com/medikoo/lru-queue
	function lruQueue$1(limit) {
	  let size = 0;
	  let base = 1;
	  let queue = Object.create(null);
	  let map = Object.create(null);
	  let index = 0;
	  const del = function (id) {
	    const oldIndex = map[id];
	    if (!oldIndex) return;
	    delete queue[oldIndex];
	    delete map[id];
	    --size;
	    if (base !== oldIndex) return;
	    if (!size) {
	      index = 0;
	      base = 1;
	      return;
	    }
	    while (!Object.prototype.hasOwnProperty.call(queue, ++base)) {/* empty */}
	  };
	  limit = Math.abs(limit);
	  return {
	    hit: function (id) {
	      const oldIndex = map[id];
	      const nuIndex = ++index;
	      queue[nuIndex] = id;
	      map[id] = nuIndex;
	      if (!oldIndex) {
	        ++size;
	        if (size <= limit) return undefined;
	        id = queue[base];
	        del(id);
	        return id;
	      }
	      delete queue[oldIndex];
	      if (base !== oldIndex) return undefined;
	      while (!Object.prototype.hasOwnProperty.call(queue, ++base)) {/* empty */}
	      return undefined;
	    },
	    delete: del,
	    clear: function () {
	      size = index = 0;
	      base = 1;
	      queue = Object.create(null);
	      map = Object.create(null);
	    }
	  };
	}
	return lruQueue;
}

var hasRequired_function$1;

function require_function$1 () {
	if (hasRequired_function$1) return _function$1;
	hasRequired_function$1 = 1;

	Object.defineProperty(_function$1, "__esModule", {
	  value: true
	});
	_function$1.memoize = memoize;
	_function$1.memoizeCompare = memoizeCompare;
	var _lruQueue = requireLruQueue();
	// function utils

	/**
	 * Memoize a given function by caching the computed result.
	 * The cache of a memoized function can be cleared by deleting the `cache`
	 * property of the function.
	 *
	 * @param {function} fn                     The function to be memoized.
	 *                                          Must be a pure function.
	 * @param {Object} [options]
	 * @param {function(args: Array): string} [options.hasher]
	 *    A custom hash builder. Is JSON.stringify by default.
	 * @param {number | undefined} [options.limit]
	 *    Maximum number of values that may be cached. Undefined indicates
	 *    unlimited (default)
	 * @return {function}                       Returns the memoized function
	 */
	function memoize(fn) {
	  let {
	    hasher,
	    limit
	  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	  limit = limit == null ? Number.POSITIVE_INFINITY : limit;
	  hasher = hasher == null ? JSON.stringify : hasher;
	  return function memoize() {
	    if (typeof memoize.cache !== 'object') {
	      memoize.cache = {
	        values: new Map(),
	        lru: (0, _lruQueue.lruQueue)(limit || Number.POSITIVE_INFINITY)
	      };
	    }
	    const args = [];
	    for (let i = 0; i < arguments.length; i++) {
	      args[i] = arguments[i];
	    }
	    const hash = hasher(args);
	    if (memoize.cache.values.has(hash)) {
	      memoize.cache.lru.hit(hash);
	      return memoize.cache.values.get(hash);
	    }
	    const newVal = fn.apply(fn, args);
	    memoize.cache.values.set(hash, newVal);
	    memoize.cache.values.delete(memoize.cache.lru.hit(hash));
	    return newVal;
	  };
	}

	/**
	 * Memoize a given function by caching all results and the arguments,
	 * and comparing against the arguments of previous results before
	 * executing again.
	 * This is less performant than `memoize` which calculates a hash,
	 * which is very fast to compare. Use `memoizeCompare` only when it is
	 * not possible to create a unique serializable hash from the function
	 * arguments.
	 * The isEqual function must compare two sets of arguments
	 * and return true when equal (can be a deep equality check for example).
	 * @param {function} fn
	 * @param {function(a: *, b: *) : boolean} isEqual
	 * @returns {function}
	 */
	function memoizeCompare(fn, isEqual) {
	  const memoize = function memoize() {
	    const args = [];
	    for (let i = 0; i < arguments.length; i++) {
	      args[i] = arguments[i];
	    }
	    for (let c = 0; c < memoize.cache.length; c++) {
	      const cached = memoize.cache[c];
	      if (isEqual(args, cached.args)) {
	        // TODO: move this cache entry to the top so recently used entries move up?
	        return cached.res;
	      }
	    }
	    const res = fn.apply(fn, args);
	    memoize.cache.unshift({
	      args,
	      res
	    });
	    return res;
	  };
	  memoize.cache = [];
	  return memoize;
	}
	return _function$1;
}

var constants$3 = {};

var hasRequiredConstants$3;

function requireConstants$3 () {
	if (hasRequiredConstants$3) return constants$3;
	hasRequiredConstants$3 = 1;

	Object.defineProperty(constants$3, "__esModule", {
	  value: true
	});
	constants$3.createBigNumberTau = constants$3.createBigNumberPi = constants$3.createBigNumberPhi = constants$3.createBigNumberE = void 0;
	var _function = require_function$1();
	/**
	 * Calculate BigNumber e
	 * @param {function} BigNumber   BigNumber constructor
	 * @returns {BigNumber} Returns e
	 */
	constants$3.createBigNumberE = (0, _function.memoize)(function (BigNumber) {
	  return new BigNumber(1).exp();
	}, {
	  hasher
	});

	/**
	 * Calculate BigNumber golden ratio, phi = (1+sqrt(5))/2
	 * @param {function} BigNumber   BigNumber constructor
	 * @returns {BigNumber} Returns phi
	 */
	constants$3.createBigNumberPhi = (0, _function.memoize)(function (BigNumber) {
	  return new BigNumber(1).plus(new BigNumber(5).sqrt()).div(2);
	}, {
	  hasher
	});

	/**
	 * Calculate BigNumber pi.
	 * @param {function} BigNumber   BigNumber constructor
	 * @returns {BigNumber} Returns pi
	 */
	const createBigNumberPi = constants$3.createBigNumberPi = (0, _function.memoize)(function (BigNumber) {
	  return BigNumber.acos(-1);
	}, {
	  hasher
	});

	/**
	 * Calculate BigNumber tau, tau = 2 * pi
	 * @param {function} BigNumber   BigNumber constructor
	 * @returns {BigNumber} Returns tau
	 */
	constants$3.createBigNumberTau = (0, _function.memoize)(function (BigNumber) {
	  return createBigNumberPi(BigNumber).times(2);
	}, {
	  hasher
	});

	/**
	 * Create a hash for a BigNumber constructor function. The created has is
	 * the configured precision
	 * @param {Array} args         Supposed to contain a single entry with
	 *                             a BigNumber constructor
	 * @return {number} precision
	 * @private
	 */
	function hasher(args) {
	  return args[0].precision;
	}
	return constants$3;
}

var hasRequiredUnit$2;

function requireUnit$2 () {
	if (hasRequiredUnit$2) return Unit;
	hasRequiredUnit$2 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(Unit, "__esModule", {
	  value: true
	});
	Unit.createUnitClass = void 0;
	var _extends2 = _interopRequireDefault(require_extends());
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _function = require_function$1();
	var _string = requireString$2();
	var _object = requireObject$3();
	var _constants = requireConstants$3();
	const name = 'Unit';
	const dependencies = ['?on', 'config', 'addScalar', 'subtractScalar', 'multiplyScalar', 'divideScalar', 'pow', 'abs', 'fix', 'round', 'equal', 'isNumeric', 'format', 'number', 'Complex', 'BigNumber', 'Fraction'];
	Unit.createUnitClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    on,
	    config,
	    addScalar,
	    subtractScalar,
	    multiplyScalar,
	    divideScalar,
	    pow,
	    abs,
	    fix,
	    round,
	    equal,
	    isNumeric,
	    format,
	    number,
	    Complex,
	    BigNumber,
	    Fraction
	  } = _ref;
	  const toNumber = number;
	  const fixPrefixDefault = false;
	  const skipAutomaticSimplificationDefault = true;

	  /**
	   * A unit can be constructed in the following ways:
	   *
	   *     const a = new Unit(value, valuelessUnit)
	   *     const b = new Unit(null, valuelessUnit)
	   *     const c = Unit.parse(str)
	   *
	   * Example usage:
	   *
	   *     const a = new Unit(5, 'cm')               // 50 mm
	   *     const b = Unit.parse('23 kg')             // 23 kg
	   *     const c = math.in(a, new Unit(null, 'm')  // 0.05 m
	   *     const d = new Unit(9.81, "m/s^2")         // 9.81 m/s^2
	   *
	   * @class Unit
	   * @constructor Unit
	   * @param {number | BigNumber | Fraction | Complex | boolean} [value]  A value like 5.2
	   * @param {string | Unit} valuelessUnit   A unit without value. Can have prefix, like "cm"
	   */
	  function Unit(value, valuelessUnit) {
	    if (!(this instanceof Unit)) {
	      throw new Error('Constructor must be called with the new operator');
	    }
	    if (!(value === null || value === undefined || isNumeric(value) || (0, _is.isComplex)(value))) {
	      throw new TypeError('First parameter in Unit constructor must be number, BigNumber, Fraction, Complex, or undefined');
	    }
	    this.fixPrefix = fixPrefixDefault; // if true, function format will not search for the
	    // best prefix but leave it as initially provided.
	    // fixPrefix is set true by the method Unit.to

	    // The justification behind this is that if the constructor is explicitly called,
	    // the caller wishes the units to be returned exactly as supplied.
	    this.skipAutomaticSimplification = skipAutomaticSimplificationDefault;
	    if (valuelessUnit === undefined) {
	      this.units = [];
	      this.dimensions = BASE_DIMENSIONS.map(x => 0);
	    } else if (typeof valuelessUnit === 'string') {
	      const u = Unit.parse(valuelessUnit);
	      this.units = u.units;
	      this.dimensions = u.dimensions;
	    } else if ((0, _is.isUnit)(valuelessUnit) && valuelessUnit.value === null) {
	      // clone from valuelessUnit
	      this.fixPrefix = valuelessUnit.fixPrefix;
	      this.skipAutomaticSimplification = valuelessUnit.skipAutomaticSimplification;
	      this.dimensions = valuelessUnit.dimensions.slice(0);
	      this.units = valuelessUnit.units.map(u => (0, _extends2.default)({}, u));
	    } else {
	      throw new TypeError('Second parameter in Unit constructor must be a string or valueless Unit');
	    }
	    this.value = this._normalize(value);
	  }

	  /**
	   * Attach type information
	   */
	  Object.defineProperty(Unit, 'name', {
	    value: 'Unit'
	  });
	  Unit.prototype.constructor = Unit;
	  Unit.prototype.type = 'Unit';
	  Unit.prototype.isUnit = true;

	  // private variables and functions for the Unit parser
	  let text, index, c;
	  function skipWhitespace() {
	    while (c === ' ' || c === '\t') {
	      next();
	    }
	  }
	  function isDigitDot(c) {
	    return c >= '0' && c <= '9' || c === '.';
	  }
	  function isDigit(c) {
	    return c >= '0' && c <= '9';
	  }
	  function next() {
	    index++;
	    c = text.charAt(index);
	  }
	  function revert(oldIndex) {
	    index = oldIndex;
	    c = text.charAt(index);
	  }
	  function parseNumber() {
	    let number = '';
	    const oldIndex = index;
	    if (c === '+') {
	      next();
	    } else if (c === '-') {
	      number += c;
	      next();
	    }
	    if (!isDigitDot(c)) {
	      // a + or - must be followed by a digit
	      revert(oldIndex);
	      return null;
	    }

	    // get number, can have a single dot
	    if (c === '.') {
	      number += c;
	      next();
	      if (!isDigit(c)) {
	        // this is no legal number, it is just a dot
	        revert(oldIndex);
	        return null;
	      }
	    } else {
	      while (isDigit(c)) {
	        number += c;
	        next();
	      }
	      if (c === '.') {
	        number += c;
	        next();
	      }
	    }
	    while (isDigit(c)) {
	      number += c;
	      next();
	    }

	    // check for exponential notation like "2.3e-4" or "1.23e50"
	    if (c === 'E' || c === 'e') {
	      // The grammar branches here. This could either be part of an exponent or the start of a unit that begins with the letter e, such as "4exabytes"

	      let tentativeNumber = '';
	      const tentativeIndex = index;
	      tentativeNumber += c;
	      next();
	      if (c === '+' || c === '-') {
	        tentativeNumber += c;
	        next();
	      }

	      // Scientific notation MUST be followed by an exponent (otherwise we assume it is not scientific notation)
	      if (!isDigit(c)) {
	        // The e or E must belong to something else, so return the number without the e or E.
	        revert(tentativeIndex);
	        return number;
	      }

	      // We can now safely say that this is scientific notation.
	      number = number + tentativeNumber;
	      while (isDigit(c)) {
	        number += c;
	        next();
	      }
	    }
	    return number;
	  }
	  function parseUnit() {
	    let unitName = '';

	    // Alphanumeric characters only; matches [a-zA-Z0-9]
	    while (isDigit(c) || Unit.isValidAlpha(c)) {
	      unitName += c;
	      next();
	    }

	    // Must begin with [a-zA-Z]
	    const firstC = unitName.charAt(0);
	    if (Unit.isValidAlpha(firstC)) {
	      return unitName;
	    } else {
	      return null;
	    }
	  }
	  function parseCharacter(toFind) {
	    if (c === toFind) {
	      next();
	      return toFind;
	    } else {
	      return null;
	    }
	  }

	  /**
	   * Parse a string into a unit. The value of the unit is parsed as number,
	   * BigNumber, or Fraction depending on the math.js config setting `number`.
	   *
	   * Throws an exception if the provided string does not contain a valid unit or
	   * cannot be parsed.
	   * @memberof Unit
	   * @param {string} str        A string like "5.2 inch", "4e2 cm/s^2"
	   * @return {Unit} unit
	   */
	  Unit.parse = function (str, options) {
	    options = options || {};
	    text = str;
	    index = -1;
	    c = '';
	    if (typeof text !== 'string') {
	      throw new TypeError('Invalid argument in Unit.parse, string expected');
	    }
	    const unit = new Unit();
	    unit.units = [];
	    let powerMultiplierCurrent = 1;
	    let expectingUnit = false;

	    // A unit should follow this pattern:
	    // [number] ...[ [*/] unit[^number] ]
	    // unit[^number] ... [ [*/] unit[^number] ]

	    // Rules:
	    // number is any floating point number.
	    // unit is any alphanumeric string beginning with an alpha. Units with names like e3 should be avoided because they look like the exponent of a floating point number!
	    // The string may optionally begin with a number.
	    // Each unit may optionally be followed by ^number.
	    // Whitespace or a forward slash is recommended between consecutive units, although the following technically is parseable:
	    //   2m^2kg/s^2
	    // it is not good form. If a unit starts with e, then it could be confused as a floating point number:
	    //   4erg

	    next();
	    skipWhitespace();

	    // Optional number at the start of the string
	    const valueStr = parseNumber();
	    let value = null;
	    if (valueStr) {
	      if (config.number === 'BigNumber') {
	        value = new BigNumber(valueStr);
	      } else if (config.number === 'Fraction') {
	        try {
	          // not all numbers can be turned in Fractions, for example very small numbers not
	          value = new Fraction(valueStr);
	        } catch (err) {
	          value = parseFloat(valueStr);
	        }
	      } else {
	        // number
	        value = parseFloat(valueStr);
	      }
	      skipWhitespace(); // Whitespace is not required here

	      // handle multiplication or division right after the value, like '1/s'
	      if (parseCharacter('*')) {
	        powerMultiplierCurrent = 1;
	        expectingUnit = true;
	      } else if (parseCharacter('/')) {
	        powerMultiplierCurrent = -1;
	        expectingUnit = true;
	      }
	    }

	    // Stack to keep track of powerMultipliers applied to each parentheses group
	    const powerMultiplierStack = [];

	    // Running product of all elements in powerMultiplierStack
	    let powerMultiplierStackProduct = 1;
	    while (true) {
	      skipWhitespace();

	      // Check for and consume opening parentheses, pushing powerMultiplierCurrent to the stack
	      // A '(' will always appear directly before a unit.
	      while (c === '(') {
	        powerMultiplierStack.push(powerMultiplierCurrent);
	        powerMultiplierStackProduct *= powerMultiplierCurrent;
	        powerMultiplierCurrent = 1;
	        next();
	        skipWhitespace();
	      }

	      // Is there something here?
	      let uStr;
	      if (c) {
	        const oldC = c;
	        uStr = parseUnit();
	        if (uStr === null) {
	          throw new SyntaxError('Unexpected "' + oldC + '" in "' + text + '" at index ' + index.toString());
	        }
	      } else {
	        // End of input.
	        break;
	      }

	      // Verify the unit exists and get the prefix (if any)
	      const res = _findUnit(uStr);
	      if (res === null) {
	        // Unit not found.
	        throw new SyntaxError('Unit "' + uStr + '" not found.');
	      }
	      let power = powerMultiplierCurrent * powerMultiplierStackProduct;
	      // Is there a "^ number"?
	      skipWhitespace();
	      if (parseCharacter('^')) {
	        skipWhitespace();
	        const p = parseNumber();
	        if (p === null) {
	          // No valid number found for the power!
	          throw new SyntaxError('In "' + str + '", "^" must be followed by a floating-point number');
	        }
	        power *= p;
	      }

	      // Add the unit to the list
	      unit.units.push({
	        unit: res.unit,
	        prefix: res.prefix,
	        power
	      });
	      for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	        unit.dimensions[i] += (res.unit.dimensions[i] || 0) * power;
	      }

	      // Check for and consume closing parentheses, popping from the stack.
	      // A ')' will always follow a unit.
	      skipWhitespace();
	      while (c === ')') {
	        if (powerMultiplierStack.length === 0) {
	          throw new SyntaxError('Unmatched ")" in "' + text + '" at index ' + index.toString());
	        }
	        powerMultiplierStackProduct /= powerMultiplierStack.pop();
	        next();
	        skipWhitespace();
	      }

	      // "*" and "/" should mean we are expecting something to come next.
	      // Is there a forward slash? If so, negate powerMultiplierCurrent. The next unit or paren group is in the denominator.
	      expectingUnit = false;
	      if (parseCharacter('*')) {
	        // explicit multiplication
	        powerMultiplierCurrent = 1;
	        expectingUnit = true;
	      } else if (parseCharacter('/')) {
	        // division
	        powerMultiplierCurrent = -1;
	        expectingUnit = true;
	      } else {
	        // implicit multiplication
	        powerMultiplierCurrent = 1;
	      }

	      // Replace the unit into the auto unit system
	      if (res.unit.base) {
	        const baseDim = res.unit.base.key;
	        UNIT_SYSTEMS.auto[baseDim] = {
	          unit: res.unit,
	          prefix: res.prefix
	        };
	      }
	    }

	    // Has the string been entirely consumed?
	    skipWhitespace();
	    if (c) {
	      throw new SyntaxError('Could not parse: "' + str + '"');
	    }

	    // Is there a trailing slash?
	    if (expectingUnit) {
	      throw new SyntaxError('Trailing characters: "' + str + '"');
	    }

	    // Is the parentheses stack empty?
	    if (powerMultiplierStack.length !== 0) {
	      throw new SyntaxError('Unmatched "(" in "' + text + '"');
	    }

	    // Are there any units at all?
	    if (unit.units.length === 0 && !options.allowNoUnits) {
	      throw new SyntaxError('"' + str + '" contains no units');
	    }
	    unit.value = value !== undefined ? unit._normalize(value) : null;
	    return unit;
	  };

	  /**
	   * create a copy of this unit
	   * @memberof Unit
	   * @return {Unit} Returns a cloned version of the unit
	   */
	  Unit.prototype.clone = function () {
	    const unit = new Unit();
	    unit.fixPrefix = this.fixPrefix;
	    unit.skipAutomaticSimplification = this.skipAutomaticSimplification;
	    unit.value = (0, _object.clone)(this.value);
	    unit.dimensions = this.dimensions.slice(0);
	    unit.units = [];
	    for (let i = 0; i < this.units.length; i++) {
	      unit.units[i] = {};
	      for (const p in this.units[i]) {
	        if ((0, _object.hasOwnProperty)(this.units[i], p)) {
	          unit.units[i][p] = this.units[i][p];
	        }
	      }
	    }
	    return unit;
	  };

	  /**
	   * Return the type of the value of this unit
	   *
	   * @memberof Unit
	   * @return {string} type of the value of the unit
	   */
	  Unit.prototype.valueType = function () {
	    return (0, _is.typeOf)(this.value);
	  };

	  /**
	   * Return whether the unit is derived (such as m/s, or cm^2, but not N)
	   * @memberof Unit
	   * @return {boolean} True if the unit is derived
	   * @private
	   */
	  Unit.prototype._isDerived = function () {
	    if (this.units.length === 0) {
	      return false;
	    }
	    return this.units.length > 1 || Math.abs(this.units[0].power - 1.0) > 1e-15;
	  };

	  /**
	   * Normalize a value, based on its currently set unit(s)
	   * @memberof Unit
	   * @param {number | BigNumber | Fraction | boolean} value
	   * @return {number | BigNumber | Fraction | boolean} normalized value
	   * @private
	   */
	  Unit.prototype._normalize = function (value) {
	    if (value === null || value === undefined || this.units.length === 0) {
	      return value;
	    }
	    let res = value;
	    const convert = Unit._getNumberConverter((0, _is.typeOf)(value)); // convert to Fraction or BigNumber if needed

	    for (let i = 0; i < this.units.length; i++) {
	      const unitValue = convert(this.units[i].unit.value);
	      const unitPrefixValue = convert(this.units[i].prefix.value);
	      const unitPower = convert(this.units[i].power);
	      res = multiplyScalar(res, pow(multiplyScalar(unitValue, unitPrefixValue), unitPower));
	    }
	    return res;
	  };

	  /**
	   * Denormalize a value, based on its currently set unit(s)
	   * @memberof Unit
	   * @param {number} value
	   * @param {number} [prefixValue]    Optional prefix value to be used (ignored if this is a derived unit)
	   * @return {number} denormalized value
	   * @private
	   */
	  Unit.prototype._denormalize = function (value, prefixValue) {
	    if (value === null || value === undefined || this.units.length === 0) {
	      return value;
	    }
	    let res = value;
	    const convert = Unit._getNumberConverter((0, _is.typeOf)(value)); // convert to Fraction or BigNumber if needed

	    for (let i = 0; i < this.units.length; i++) {
	      const unitValue = convert(this.units[i].unit.value);
	      const unitPrefixValue = convert(this.units[i].prefix.value);
	      const unitPower = convert(this.units[i].power);
	      res = divideScalar(res, pow(multiplyScalar(unitValue, unitPrefixValue), unitPower));
	    }
	    return res;
	  };

	  /**
	   * Find a unit from a string
	   * @memberof Unit
	   * @param {string} str              A string like 'cm' or 'inch'
	   * @returns {Object | null} result  When found, an object with fields unit and
	   *                                  prefix is returned. Else, null is returned.
	   * @private
	   */
	  const _findUnit = (0, _function.memoize)(str => {
	    // First, match units names exactly. For example, a user could define 'mm' as 10^-4 m, which is silly, but then we would want 'mm' to match the user-defined unit.
	    if ((0, _object.hasOwnProperty)(UNITS, str)) {
	      const unit = UNITS[str];
	      const prefix = unit.prefixes[''];
	      return {
	        unit,
	        prefix
	      };
	    }
	    for (const name in UNITS) {
	      if ((0, _object.hasOwnProperty)(UNITS, name)) {
	        if ((0, _string.endsWith)(str, name)) {
	          const unit = UNITS[name];
	          const prefixLen = str.length - name.length;
	          const prefixName = str.substring(0, prefixLen);
	          const prefix = (0, _object.hasOwnProperty)(unit.prefixes, prefixName) ? unit.prefixes[prefixName] : undefined;
	          if (prefix !== undefined) {
	            // store unit, prefix, and value
	            return {
	              unit,
	              prefix
	            };
	          }
	        }
	      }
	    }
	    return null;
	  }, {
	    hasher: args => args[0],
	    limit: 100
	  });

	  /**
	   * Test if the given expression is a unit.
	   * The unit can have a prefix but cannot have a value.
	   * @memberof Unit
	   * @param {string} name   A string to be tested whether it is a value less unit.
	   *                        The unit can have prefix, like "cm"
	   * @return {boolean}      true if the given string is a unit
	   */
	  Unit.isValuelessUnit = function (name) {
	    return _findUnit(name) !== null;
	  };

	  /**
	   * check if this unit has given base unit
	   * If this unit is a derived unit, this will ALWAYS return false, since by definition base units are not derived.
	   * @memberof Unit
	   * @param {BASE_UNIT | string | undefined} base
	   */
	  Unit.prototype.hasBase = function (base) {
	    if (typeof base === 'string') {
	      base = BASE_UNITS[base];
	    }
	    if (!base) {
	      return false;
	    }

	    // All dimensions must be the same
	    for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	      if (Math.abs((this.dimensions[i] || 0) - (base.dimensions[i] || 0)) > 1e-12) {
	        return false;
	      }
	    }
	    return true;
	  };

	  /**
	   * Check if this unit has a base or bases equal to another base or bases
	   * For derived units, the exponent on each base also must match
	   * @memberof Unit
	   * @param {Unit} other
	   * @return {boolean} true if equal base
	   */
	  Unit.prototype.equalBase = function (other) {
	    // All dimensions must be the same
	    for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	      if (Math.abs((this.dimensions[i] || 0) - (other.dimensions[i] || 0)) > 1e-12) {
	        return false;
	      }
	    }
	    return true;
	  };

	  /**
	   * Check if this unit equals another unit
	   * @memberof Unit
	   * @param {Unit} other
	   * @return {boolean} true if both units are equal
	   */
	  Unit.prototype.equals = function (other) {
	    return this.equalBase(other) && equal(this.value, other.value);
	  };

	  /**
	   * Multiply this unit with another one or with a scalar
	   * @memberof Unit
	   * @param {Unit} other
	   * @return {Unit} product of this unit and the other unit
	   */
	  Unit.prototype.multiply = function (_other) {
	    const res = this.clone();
	    const other = (0, _is.isUnit)(_other) ? _other : new Unit(_other);
	    for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	      // Dimensions arrays may be of different lengths. Default to 0.
	      res.dimensions[i] = (this.dimensions[i] || 0) + (other.dimensions[i] || 0);
	    }

	    // Append other's units list onto res
	    for (let i = 0; i < other.units.length; i++) {
	      // Make a shallow copy of every unit
	      const inverted = {
	        ...other.units[i]
	      };
	      res.units.push(inverted);
	    }

	    // If at least one operand has a value, then the result should also have a value
	    if (this.value !== null || other.value !== null) {
	      const valThis = this.value === null ? this._normalize(one(other.value)) : this.value;
	      const valOther = other.value === null ? other._normalize(one(this.value)) : other.value;
	      res.value = multiplyScalar(valThis, valOther);
	    } else {
	      res.value = null;
	    }
	    if ((0, _is.isUnit)(_other)) {
	      res.skipAutomaticSimplification = false;
	    }
	    return getNumericIfUnitless(res);
	  };

	  /**
	   * Divide a number by this unit
	   *
	   * @memberof Unit
	   * @param {numeric} numerator
	   * @param {unit} result of dividing numerator by this unit
	   */
	  Unit.prototype.divideInto = function (numerator) {
	    return new Unit(numerator).divide(this);
	  };

	  /**
	   * Divide this unit by another one
	   * @memberof Unit
	   * @param {Unit | numeric} other
	   * @return {Unit} result of dividing this unit by the other unit
	   */
	  Unit.prototype.divide = function (_other) {
	    const res = this.clone();
	    const other = (0, _is.isUnit)(_other) ? _other : new Unit(_other);
	    for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	      // Dimensions arrays may be of different lengths. Default to 0.
	      res.dimensions[i] = (this.dimensions[i] || 0) - (other.dimensions[i] || 0);
	    }

	    // Invert and append other's units list onto res
	    for (let i = 0; i < other.units.length; i++) {
	      // Make a shallow copy of every unit
	      const inverted = {
	        ...other.units[i],
	        power: -other.units[i].power
	      };
	      res.units.push(inverted);
	    }

	    // If at least one operand has a value, the result should have a value
	    if (this.value !== null || other.value !== null) {
	      const valThis = this.value === null ? this._normalize(one(other.value)) : this.value;
	      const valOther = other.value === null ? other._normalize(one(this.value)) : other.value;
	      res.value = divideScalar(valThis, valOther);
	    } else {
	      res.value = null;
	    }
	    if ((0, _is.isUnit)(_other)) {
	      res.skipAutomaticSimplification = false;
	    }
	    return getNumericIfUnitless(res);
	  };

	  /**
	   * Calculate the power of a unit
	   * @memberof Unit
	   * @param {number | Fraction | BigNumber} p
	   * @returns {Unit}      The result: this^p
	   */
	  Unit.prototype.pow = function (p) {
	    const res = this.clone();
	    for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	      // Dimensions arrays may be of different lengths. Default to 0.
	      res.dimensions[i] = (this.dimensions[i] || 0) * p;
	    }

	    // Adjust the power of each unit in the list
	    for (let i = 0; i < res.units.length; i++) {
	      res.units[i].power *= p;
	    }
	    if (res.value !== null) {
	      res.value = pow(res.value, p);

	      // only allow numeric output, we don't want to return a Complex number
	      // if (!isNumeric(res.value)) {
	      //  res.value = NaN
	      // }
	      // Update: Complex supported now
	    } else {
	      res.value = null;
	    }
	    res.skipAutomaticSimplification = false;
	    return getNumericIfUnitless(res);
	  };

	  /**
	   * Return the numeric value of this unit if it is dimensionless, has a value, and config.predictable == false; or the original unit otherwise
	   * @param {Unit} unit
	   * @returns {number | Fraction | BigNumber | Unit}  The numeric value of the unit if conditions are met, or the original unit otherwise
	   */
	  function getNumericIfUnitless(unit) {
	    if (unit.equalBase(BASE_UNITS.NONE) && unit.value !== null && !config.predictable) {
	      return unit.value;
	    } else {
	      return unit;
	    }
	  }

	  /**
	   * Create a value one with the numeric type of `typeOfValue`.
	   * For example, `one(new BigNumber(3))` returns `BigNumber(1)`
	   * @param {number | Fraction | BigNumber} typeOfValue
	   * @returns {number | Fraction | BigNumber}
	   */
	  function one(typeOfValue) {
	    // TODO: this is a workaround to prevent the following BigNumber conversion error from throwing:
	    //  "TypeError: Cannot implicitly convert a number with >15 significant digits to BigNumber"
	    //  see https://github.com/josdejong/mathjs/issues/3450
	    //      https://github.com/josdejong/mathjs/pull/3375
	    const convert = Unit._getNumberConverter((0, _is.typeOf)(typeOfValue));
	    return convert(1);
	  }

	  /**
	   * Calculate the absolute value of a unit
	   * @memberof Unit
	   * @param {number | Fraction | BigNumber} x
	   * @returns {Unit}      The result: |x|, absolute value of x
	   */
	  Unit.prototype.abs = function () {
	    const ret = this.clone();
	    if (ret.value !== null) {
	      if (ret._isDerived() || ret.units.length === 0 || ret.units[0].unit.offset === 0) {
	        ret.value = abs(ret.value);
	      } else {
	        // To give the correct, but unexpected, results for units with an offset.
	        // For example, abs(-283.15 degC) = -263.15 degC !!!
	        // We must take the offset into consideration here
	        const convert = ret._numberConverter(); // convert to Fraction or BigNumber if needed
	        const unitValue = convert(ret.units[0].unit.value);
	        const nominalOffset = convert(ret.units[0].unit.offset);
	        const unitOffset = multiplyScalar(unitValue, nominalOffset);
	        ret.value = subtractScalar(abs(addScalar(ret.value, unitOffset)), unitOffset);
	      }
	    }
	    for (const i in ret.units) {
	      if (ret.units[i].unit.name === 'VA' || ret.units[i].unit.name === 'VAR') {
	        ret.units[i].unit = UNITS.W;
	      }
	    }
	    return ret;
	  };

	  /**
	   * Convert the unit to a specific unit name.
	   * @memberof Unit
	   * @param {string | Unit} valuelessUnit   A unit without value. Can have prefix, like "cm"
	   * @returns {Unit} Returns a clone of the unit with a fixed prefix and unit.
	   */
	  Unit.prototype.to = function (valuelessUnit) {
	    const value = this.value === null ? this._normalize(1) : this.value;
	    let other;
	    if (typeof valuelessUnit === 'string') {
	      other = Unit.parse(valuelessUnit);
	    } else if ((0, _is.isUnit)(valuelessUnit)) {
	      other = valuelessUnit.clone();
	    } else {
	      throw new Error('String or Unit expected as parameter');
	    }
	    if (!this.equalBase(other)) {
	      throw new Error(`Units do not match ('${other.toString()}' != '${this.toString()}')`);
	    }
	    if (other.value !== null) {
	      throw new Error('Cannot convert to a unit with a value');
	    }
	    if (this.value === null || this._isDerived() || this.units.length === 0 || other.units.length === 0 || this.units[0].unit.offset === other.units[0].unit.offset) {
	      other.value = (0, _object.clone)(value);
	    } else {
	      /* Need to adjust value by difference in offset to convert */
	      const convert = Unit._getNumberConverter((0, _is.typeOf)(value)); // convert to Fraction or BigNumber if needed

	      const thisUnitValue = this.units[0].unit.value;
	      const thisNominalOffset = this.units[0].unit.offset;
	      const thisUnitOffset = multiplyScalar(thisUnitValue, thisNominalOffset);
	      const otherUnitValue = other.units[0].unit.value;
	      const otherNominalOffset = other.units[0].unit.offset;
	      const otherUnitOffset = multiplyScalar(otherUnitValue, otherNominalOffset);
	      other.value = addScalar(value, convert(subtractScalar(thisUnitOffset, otherUnitOffset)));
	    }
	    other.fixPrefix = true;
	    other.skipAutomaticSimplification = true;
	    return other;
	  };

	  /**
	   * Return the value of the unit when represented with given valueless unit
	   * @memberof Unit
	   * @param {string | Unit} valuelessUnit    For example 'cm' or 'inch'
	   * @return {number} Returns the unit value as number.
	   */
	  // TODO: deprecate Unit.toNumber? It's always better to use toNumeric
	  Unit.prototype.toNumber = function (valuelessUnit) {
	    return toNumber(this.toNumeric(valuelessUnit));
	  };

	  /**
	   * Return the value of the unit in the original numeric type
	   * @memberof Unit
	   * @param {string | Unit} valuelessUnit    For example 'cm' or 'inch'
	   * @return {number | BigNumber | Fraction} Returns the unit value
	   */
	  Unit.prototype.toNumeric = function (valuelessUnit) {
	    let other;
	    if (valuelessUnit) {
	      // Allow getting the numeric value without converting to a different unit
	      other = this.to(valuelessUnit);
	    } else {
	      other = this.clone();
	    }
	    if (other._isDerived() || other.units.length === 0) {
	      return other._denormalize(other.value);
	    } else {
	      return other._denormalize(other.value, other.units[0].prefix.value);
	    }
	  };

	  /**
	   * Get a string representation of the unit.
	   * @memberof Unit
	   * @return {string}
	   */
	  Unit.prototype.toString = function () {
	    return this.format();
	  };

	  /**
	   * Get a JSON representation of the unit
	   * @memberof Unit
	   * @returns {Object} Returns a JSON object structured as:
	   *                   `{"mathjs": "Unit", "value": 2, "unit": "cm", "fixPrefix": false, "skipSimp": true}`
	   */
	  Unit.prototype.toJSON = function () {
	    return {
	      mathjs: 'Unit',
	      value: this._denormalize(this.value),
	      unit: this.units.length > 0 ? this.formatUnits() : null,
	      fixPrefix: this.fixPrefix,
	      skipSimp: this.skipAutomaticSimplification
	    };
	  };

	  /**
	   * Instantiate a Unit from a JSON object
	   * @memberof Unit
	   * @param {Object} json  A JSON object structured as:
	   *                       `{"mathjs": "Unit", "value": 2, "unit": "cm", "fixPrefix": false}`
	   * @return {Unit}
	   */
	  Unit.fromJSON = function (json) {
	    var _json$unit, _json$fixPrefix, _json$skipSimp;
	    const unit = new Unit(json.value, (_json$unit = json.unit) !== null && _json$unit !== void 0 ? _json$unit : undefined);
	    unit.fixPrefix = (_json$fixPrefix = json.fixPrefix) !== null && _json$fixPrefix !== void 0 ? _json$fixPrefix : fixPrefixDefault;
	    unit.skipAutomaticSimplification = (_json$skipSimp = json.skipSimp) !== null && _json$skipSimp !== void 0 ? _json$skipSimp : skipAutomaticSimplificationDefault;
	    return unit;
	  };

	  /**
	   * Returns the string representation of the unit.
	   * @memberof Unit
	   * @return {string}
	   */
	  Unit.prototype.valueOf = Unit.prototype.toString;

	  /**
	   * Simplify this Unit's unit list and return a new Unit with the simplified list.
	   * The returned Unit will contain a list of the "best" units for formatting.
	   */
	  Unit.prototype.simplify = function () {
	    const ret = this.clone();
	    const proposedUnitList = [];

	    // Search for a matching base
	    let matchingBase;
	    for (const key in currentUnitSystem) {
	      if ((0, _object.hasOwnProperty)(currentUnitSystem, key)) {
	        if (ret.hasBase(BASE_UNITS[key])) {
	          matchingBase = key;
	          break;
	        }
	      }
	    }
	    if (matchingBase === 'NONE') {
	      ret.units = [];
	    } else {
	      let matchingUnit;
	      if (matchingBase) {
	        // Does the unit system have a matching unit?
	        if ((0, _object.hasOwnProperty)(currentUnitSystem, matchingBase)) {
	          matchingUnit = currentUnitSystem[matchingBase];
	        }
	      }
	      if (matchingUnit) {
	        ret.units = [{
	          unit: matchingUnit.unit,
	          prefix: matchingUnit.prefix,
	          power: 1.0
	        }];
	      } else {
	        // Multiple units or units with powers are formatted like this:
	        // 5 (kg m^2) / (s^3 mol)
	        // Build an representation from the base units of the current unit system
	        let missingBaseDim = false;
	        for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	          const baseDim = BASE_DIMENSIONS[i];
	          if (Math.abs(ret.dimensions[i] || 0) > 1e-12) {
	            if ((0, _object.hasOwnProperty)(currentUnitSystem, baseDim)) {
	              proposedUnitList.push({
	                unit: currentUnitSystem[baseDim].unit,
	                prefix: currentUnitSystem[baseDim].prefix,
	                power: ret.dimensions[i] || 0
	              });
	            } else {
	              missingBaseDim = true;
	            }
	          }
	        }

	        // Is the proposed unit list "simpler" than the existing one?
	        if (proposedUnitList.length < ret.units.length && !missingBaseDim) {
	          // Replace this unit list with the proposed list
	          ret.units = proposedUnitList;
	        }
	      }
	    }
	    return ret;
	  };

	  /**
	   * Returns a new Unit in the SI system with the same value as this one
	   */
	  Unit.prototype.toSI = function () {
	    const ret = this.clone();
	    const proposedUnitList = [];

	    // Multiple units or units with powers are formatted like this:
	    // 5 (kg m^2) / (s^3 mol)
	    // Build an representation from the base units of the SI unit system
	    for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	      const baseDim = BASE_DIMENSIONS[i];
	      if (Math.abs(ret.dimensions[i] || 0) > 1e-12) {
	        if ((0, _object.hasOwnProperty)(UNIT_SYSTEMS.si, baseDim)) {
	          proposedUnitList.push({
	            unit: UNIT_SYSTEMS.si[baseDim].unit,
	            prefix: UNIT_SYSTEMS.si[baseDim].prefix,
	            power: ret.dimensions[i] || 0
	          });
	        } else {
	          throw new Error('Cannot express custom unit ' + baseDim + ' in SI units');
	        }
	      }
	    }

	    // Replace this unit list with the proposed list
	    ret.units = proposedUnitList;
	    ret.fixPrefix = true;
	    ret.skipAutomaticSimplification = true;
	    if (this.value !== null) {
	      ret.value = null;
	      return this.to(ret);
	    }
	    return ret;
	  };

	  /**
	   * Get a string representation of the units of this Unit, without the value. The unit list is formatted as-is without first being simplified.
	   * @memberof Unit
	   * @return {string}
	   */
	  Unit.prototype.formatUnits = function () {
	    let strNum = '';
	    let strDen = '';
	    let nNum = 0;
	    let nDen = 0;
	    for (let i = 0; i < this.units.length; i++) {
	      if (this.units[i].power > 0) {
	        nNum++;
	        strNum += ' ' + this.units[i].prefix.name + this.units[i].unit.name;
	        if (Math.abs(this.units[i].power - 1.0) > 1e-15) {
	          strNum += '^' + this.units[i].power;
	        }
	      } else if (this.units[i].power < 0) {
	        nDen++;
	      }
	    }
	    if (nDen > 0) {
	      for (let i = 0; i < this.units.length; i++) {
	        if (this.units[i].power < 0) {
	          if (nNum > 0) {
	            strDen += ' ' + this.units[i].prefix.name + this.units[i].unit.name;
	            if (Math.abs(this.units[i].power + 1.0) > 1e-15) {
	              strDen += '^' + -this.units[i].power;
	            }
	          } else {
	            strDen += ' ' + this.units[i].prefix.name + this.units[i].unit.name;
	            strDen += '^' + this.units[i].power;
	          }
	        }
	      }
	    }
	    // Remove leading " "
	    strNum = strNum.substr(1);
	    strDen = strDen.substr(1);

	    // Add parans for better copy/paste back into evaluate, for example, or for better pretty print formatting
	    if (nNum > 1 && nDen > 0) {
	      strNum = '(' + strNum + ')';
	    }
	    if (nDen > 1 && nNum > 0) {
	      strDen = '(' + strDen + ')';
	    }
	    let str = strNum;
	    if (nNum > 0 && nDen > 0) {
	      str += ' / ';
	    }
	    str += strDen;
	    return str;
	  };

	  /**
	   * Get a unit, with optional formatting options.
	   * @memberof Unit
	   * @param {string[] | Unit[]} [units]  Array of units strings or valueLess Unit objects in wich choose the best one
	   * @param {Object} [options]  Options for parsing the unit. See parseUnit for details.
	   *
	   * @return {Unit} Returns a new Unit with the given value and unit.
	   */
	  Unit.prototype.toBest = function () {
	    let unitList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
	    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	    if (unitList && !Array.isArray(unitList)) {
	      throw new Error('Invalid unit type. Expected string or Unit.');
	    }
	    const startPrefixes = this.units[0].unit.prefixes;
	    if (unitList && unitList.length > 0) {
	      const unitObjects = unitList.map(u => {
	        let unit = null;
	        if (typeof u === 'string') {
	          unit = Unit.parse(u);
	          if (!unit) {
	            throw new Error('Invalid unit type. Expected compatible string or Unit.');
	          }
	        } else if (!(0, _is.isUnit)(u)) {
	          throw new Error('Invalid unit type. Expected compatible string or Unit.');
	        }
	        if (unit === null) {
	          unit = u.clone();
	        }
	        try {
	          this.to(unit.formatUnits());
	          return unit;
	        } catch (e) {
	          throw new Error('Invalid unit type. Expected compatible string or Unit.');
	        }
	      });
	      const prefixes = unitObjects.map(el => el.units[0].prefix);
	      this.units[0].unit.prefixes = prefixes.reduce((acc, prefix) => {
	        acc[prefix.name] = prefix;
	        return acc;
	      }, {});
	      this.units[0].prefix = prefixes[0];
	    }
	    const result = formatBest(this, options).simp;
	    this.units[0].unit.prefixes = startPrefixes;
	    result.fixPrefix = true;
	    return result;
	  };
	  /**
	   * Get a string representation of the Unit, with optional formatting options.
	   * @memberof Unit
	   * @param {Object | number | Function} [options]  Formatting options. See
	   *                                                lib/utils/number:format for a
	   *                                                description of the available
	   *                                                options.
	   * @return {string}
	   */
	  Unit.prototype.format = function (options) {
	    const {
	      simp,
	      valueStr,
	      unitStr
	    } = formatBest(this, options);
	    let str = valueStr;
	    if (simp.value && (0, _is.isComplex)(simp.value)) {
	      str = '(' + str + ')'; // Surround complex values with ( ) to enable better parsing
	    }
	    if (unitStr.length > 0 && str.length > 0) {
	      str += ' ';
	    }
	    str += unitStr;
	    return str;
	  };

	  /**
	  * Helper function to normalize a unit for conversion and formatting
	  * @param {Unit} unit The unit to be normalized
	  * @return {Object} Object with normalized unit and value
	  * @private
	  */
	  function formatBest(unit) {
	    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	    // Simplfy the unit list, unless it is valueless or was created directly in the
	    // constructor or as the result of to or toSI
	    const simp = unit.skipAutomaticSimplification || unit.value === null ? unit.clone() : unit.simplify();

	    // Apply some custom logic for handling VA and VAR. The goal is to express the value of the unit as a real value, if possible. Otherwise, use a real-valued unit instead of a complex-valued one.
	    handleVAandVARUnits(simp);
	    // Now apply the best prefix
	    // Units must have only one unit and not have the fixPrefix flag set
	    applyBestPrefixIfNeeded(simp, options.offset);
	    const value = simp._denormalize(simp.value);
	    const valueStr = simp.value !== null ? format(value, options || {}) : '';
	    const unitStr = simp.formatUnits();
	    return {
	      simp,
	      valueStr,
	      unitStr
	    };
	  }

	  /**
	   * Helper to handle VA and VAR units
	   * @param {Unit} simp The unit to be normalized
	   */
	  function handleVAandVARUnits(simp) {
	    let isImaginary = false;
	    if (typeof simp.value !== 'undefined' && simp.value !== null && (0, _is.isComplex)(simp.value)) {
	      // TODO: Make this better, for example, use relative magnitude of re and im rather than absolute
	      isImaginary = Math.abs(simp.value.re) < 1e-14;
	    }
	    for (const i in simp.units) {
	      if ((0, _object.hasOwnProperty)(simp.units, i)) {
	        if (simp.units[i].unit) {
	          if (simp.units[i].unit.name === 'VA' && isImaginary) {
	            simp.units[i].unit = UNITS.VAR;
	          } else if (simp.units[i].unit.name === 'VAR' && !isImaginary) {
	            simp.units[i].unit = UNITS.VA;
	          }
	        }
	      }
	    }
	  }

	  /**
	   * Helper to apply the best prefix if needed
	   * @param {Unit} simp The unit to be normalized
	   */
	  function applyBestPrefixIfNeeded(simp, offset) {
	    if (simp.units.length === 1 && !simp.fixPrefix) {
	      // Units must have integer powers, otherwise the prefix will change the
	      // outputted value by not-an-integer-power-of-ten
	      if (Math.abs(simp.units[0].power - Math.round(simp.units[0].power)) < 1e-14) {
	        // Apply the best prefix
	        simp.units[0].prefix = simp._bestPrefix(offset);
	      }
	    }
	  }

	  /**
	   * Calculate the best prefix using current value.
	   * @memberof Unit
	   * @returns {Object} prefix
	   * @param {number} [offset]  Optional offset for the best prefix calculation (default 1.2)
	   * @private
	   */
	  Unit.prototype._bestPrefix = function () {
	    let offset = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1.2;
	    if (this.units.length !== 1) {
	      throw new Error('Can only compute the best prefix for single units with integer powers, like kg, s^2, N^-1, and so forth!');
	    }
	    if (Math.abs(this.units[0].power - Math.round(this.units[0].power)) >= 1e-14) {
	      throw new Error('Can only compute the best prefix for single units with integer powers, like kg, s^2, N^-1, and so forth!');
	    }

	    // find the best prefix value (resulting in the value of which
	    // the absolute value of the log10 is closest to zero,
	    // though with a little offset of 1.2 for nicer values: you get a
	    // sequence 1mm 100mm 500mm 0.6m 1m 10m 100m 500m 0.6km 1km ...

	    // Note: the units value can be any numeric type, but to find the best
	    // prefix it's enough to work with limited precision of a regular number
	    // Update: using mathjs abs since we also allow complex numbers
	    const absValue = this.value !== null ? abs(this.value) : 0;
	    const absUnitValue = abs(this.units[0].unit.value);
	    let bestPrefix = this.units[0].prefix;
	    if (absValue === 0) {
	      return bestPrefix;
	    }
	    const power = this.units[0].power;
	    let bestDiff = Math.log(absValue / Math.pow(bestPrefix.value * absUnitValue, power)) / Math.LN10 - offset;
	    if (bestDiff > -2.200001 && bestDiff < 1.800001) return bestPrefix; // Allow the original prefix
	    bestDiff = Math.abs(bestDiff);
	    const prefixes = this.units[0].unit.prefixes;
	    for (const p in prefixes) {
	      if ((0, _object.hasOwnProperty)(prefixes, p)) {
	        const prefix = prefixes[p];
	        if (prefix.scientific) {
	          const diff = Math.abs(Math.log(absValue / Math.pow(prefix.value * absUnitValue, power)) / Math.LN10 - offset);
	          if (diff < bestDiff || diff === bestDiff && prefix.name.length < bestPrefix.name.length) {
	            // choose the prefix with the smallest diff, or if equal, choose the one
	            // with the shortest name (can happen with SHORTLONG for example)
	            bestPrefix = prefix;
	            bestDiff = diff;
	          }
	        }
	      }
	    }
	    return bestPrefix;
	  };

	  /**
	   * Returns an array of units whose sum is equal to this unit
	   * @memberof Unit
	   * @param {Array} [parts] An array of strings or valueless units.
	   *
	   *   Example:
	   *
	   *   const u = new Unit(1, 'm')
	   *   u.splitUnit(['feet', 'inch'])
	   *     [ 3 feet, 3.3700787401575 inch ]
	   *
	   * @return {Array} An array of units.
	   */
	  Unit.prototype.splitUnit = function (parts) {
	    let x = this.clone();
	    const ret = [];
	    for (let i = 0; i < parts.length; i++) {
	      // Convert x to the requested unit
	      x = x.to(parts[i]);
	      if (i === parts.length - 1) break;

	      // Get the numeric value of this unit
	      const xNumeric = x.toNumeric();

	      // Check to see if xNumeric is nearly equal to an integer,
	      // since fix can incorrectly round down if there is round-off error
	      const xRounded = round(xNumeric);
	      let xFixed;
	      const isNearlyEqual = equal(xRounded, xNumeric);
	      if (isNearlyEqual) {
	        xFixed = xRounded;
	      } else {
	        xFixed = fix(x.toNumeric());
	      }
	      const y = new Unit(xFixed, parts[i].toString());
	      ret.push(y);
	      x = subtractScalar(x, y);
	    }

	    // This little bit fixes a bug where the remainder should be 0 but is a little bit off.
	    // But instead of comparing x, the remainder, with zero--we will compare the sum of
	    // all the parts so far with the original value. If they are nearly equal,
	    // we set the remainder to 0.
	    let testSum = 0;
	    for (let i = 0; i < ret.length; i++) {
	      testSum = addScalar(testSum, ret[i].value);
	    }
	    if (equal(testSum, this.value)) {
	      x.value = 0;
	    }
	    ret.push(x);
	    return ret;
	  };
	  const PREFIXES = {
	    NONE: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      }
	    },
	    SHORT: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      da: {
	        name: 'da',
	        value: 1e1,
	        scientific: false
	      },
	      h: {
	        name: 'h',
	        value: 1e2,
	        scientific: false
	      },
	      k: {
	        name: 'k',
	        value: 1e3,
	        scientific: true
	      },
	      M: {
	        name: 'M',
	        value: 1e6,
	        scientific: true
	      },
	      G: {
	        name: 'G',
	        value: 1e9,
	        scientific: true
	      },
	      T: {
	        name: 'T',
	        value: 1e12,
	        scientific: true
	      },
	      P: {
	        name: 'P',
	        value: 1e15,
	        scientific: true
	      },
	      E: {
	        name: 'E',
	        value: 1e18,
	        scientific: true
	      },
	      Z: {
	        name: 'Z',
	        value: 1e21,
	        scientific: true
	      },
	      Y: {
	        name: 'Y',
	        value: 1e24,
	        scientific: true
	      },
	      R: {
	        name: 'R',
	        value: 1e27,
	        scientific: true
	      },
	      Q: {
	        name: 'Q',
	        value: 1e30,
	        scientific: true
	      },
	      d: {
	        name: 'd',
	        value: 1e-1,
	        scientific: false
	      },
	      c: {
	        name: 'c',
	        value: 1e-2,
	        scientific: false
	      },
	      m: {
	        name: 'm',
	        value: 1e-3,
	        scientific: true
	      },
	      u: {
	        name: 'u',
	        value: 1e-6,
	        scientific: true
	      },
	      n: {
	        name: 'n',
	        value: 1e-9,
	        scientific: true
	      },
	      p: {
	        name: 'p',
	        value: 1e-12,
	        scientific: true
	      },
	      f: {
	        name: 'f',
	        value: 1e-15,
	        scientific: true
	      },
	      a: {
	        name: 'a',
	        value: 1e-18,
	        scientific: true
	      },
	      z: {
	        name: 'z',
	        value: 1e-21,
	        scientific: true
	      },
	      y: {
	        name: 'y',
	        value: 1e-24,
	        scientific: true
	      },
	      r: {
	        name: 'r',
	        value: 1e-27,
	        scientific: true
	      },
	      q: {
	        name: 'q',
	        value: 1e-30,
	        scientific: true
	      }
	    },
	    LONG: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      deca: {
	        name: 'deca',
	        value: 1e1,
	        scientific: false
	      },
	      hecto: {
	        name: 'hecto',
	        value: 1e2,
	        scientific: false
	      },
	      kilo: {
	        name: 'kilo',
	        value: 1e3,
	        scientific: true
	      },
	      mega: {
	        name: 'mega',
	        value: 1e6,
	        scientific: true
	      },
	      giga: {
	        name: 'giga',
	        value: 1e9,
	        scientific: true
	      },
	      tera: {
	        name: 'tera',
	        value: 1e12,
	        scientific: true
	      },
	      peta: {
	        name: 'peta',
	        value: 1e15,
	        scientific: true
	      },
	      exa: {
	        name: 'exa',
	        value: 1e18,
	        scientific: true
	      },
	      zetta: {
	        name: 'zetta',
	        value: 1e21,
	        scientific: true
	      },
	      yotta: {
	        name: 'yotta',
	        value: 1e24,
	        scientific: true
	      },
	      ronna: {
	        name: 'ronna',
	        value: 1e27,
	        scientific: true
	      },
	      quetta: {
	        name: 'quetta',
	        value: 1e30,
	        scientific: true
	      },
	      deci: {
	        name: 'deci',
	        value: 1e-1,
	        scientific: false
	      },
	      centi: {
	        name: 'centi',
	        value: 1e-2,
	        scientific: false
	      },
	      milli: {
	        name: 'milli',
	        value: 1e-3,
	        scientific: true
	      },
	      micro: {
	        name: 'micro',
	        value: 1e-6,
	        scientific: true
	      },
	      nano: {
	        name: 'nano',
	        value: 1e-9,
	        scientific: true
	      },
	      pico: {
	        name: 'pico',
	        value: 1e-12,
	        scientific: true
	      },
	      femto: {
	        name: 'femto',
	        value: 1e-15,
	        scientific: true
	      },
	      atto: {
	        name: 'atto',
	        value: 1e-18,
	        scientific: true
	      },
	      zepto: {
	        name: 'zepto',
	        value: 1e-21,
	        scientific: true
	      },
	      yocto: {
	        name: 'yocto',
	        value: 1e-24,
	        scientific: true
	      },
	      ronto: {
	        name: 'ronto',
	        value: 1e-27,
	        scientific: true
	      },
	      quecto: {
	        name: 'quecto',
	        value: 1e-30,
	        scientific: true
	      }
	    },
	    SQUARED: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      da: {
	        name: 'da',
	        value: 1e2,
	        scientific: false
	      },
	      h: {
	        name: 'h',
	        value: 1e4,
	        scientific: false
	      },
	      k: {
	        name: 'k',
	        value: 1e6,
	        scientific: true
	      },
	      M: {
	        name: 'M',
	        value: 1e12,
	        scientific: true
	      },
	      G: {
	        name: 'G',
	        value: 1e18,
	        scientific: true
	      },
	      T: {
	        name: 'T',
	        value: 1e24,
	        scientific: true
	      },
	      P: {
	        name: 'P',
	        value: 1e30,
	        scientific: true
	      },
	      E: {
	        name: 'E',
	        value: 1e36,
	        scientific: true
	      },
	      Z: {
	        name: 'Z',
	        value: 1e42,
	        scientific: true
	      },
	      Y: {
	        name: 'Y',
	        value: 1e48,
	        scientific: true
	      },
	      R: {
	        name: 'R',
	        value: 1e54,
	        scientific: true
	      },
	      Q: {
	        name: 'Q',
	        value: 1e60,
	        scientific: true
	      },
	      d: {
	        name: 'd',
	        value: 1e-2,
	        scientific: false
	      },
	      c: {
	        name: 'c',
	        value: 1e-4,
	        scientific: false
	      },
	      m: {
	        name: 'm',
	        value: 1e-6,
	        scientific: true
	      },
	      u: {
	        name: 'u',
	        value: 1e-12,
	        scientific: true
	      },
	      n: {
	        name: 'n',
	        value: 1e-18,
	        scientific: true
	      },
	      p: {
	        name: 'p',
	        value: 1e-24,
	        scientific: true
	      },
	      f: {
	        name: 'f',
	        value: 1e-30,
	        scientific: true
	      },
	      a: {
	        name: 'a',
	        value: 1e-36,
	        scientific: true
	      },
	      z: {
	        name: 'z',
	        value: 1e-42,
	        scientific: true
	      },
	      y: {
	        name: 'y',
	        value: 1e-48,
	        scientific: true
	      },
	      r: {
	        name: 'r',
	        value: 1e-54,
	        scientific: true
	      },
	      q: {
	        name: 'q',
	        value: 1e-60,
	        scientific: true
	      }
	    },
	    CUBIC: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      da: {
	        name: 'da',
	        value: 1e3,
	        scientific: false
	      },
	      h: {
	        name: 'h',
	        value: 1e6,
	        scientific: false
	      },
	      k: {
	        name: 'k',
	        value: 1e9,
	        scientific: true
	      },
	      M: {
	        name: 'M',
	        value: 1e18,
	        scientific: true
	      },
	      G: {
	        name: 'G',
	        value: 1e27,
	        scientific: true
	      },
	      T: {
	        name: 'T',
	        value: 1e36,
	        scientific: true
	      },
	      P: {
	        name: 'P',
	        value: 1e45,
	        scientific: true
	      },
	      E: {
	        name: 'E',
	        value: 1e54,
	        scientific: true
	      },
	      Z: {
	        name: 'Z',
	        value: 1e63,
	        scientific: true
	      },
	      Y: {
	        name: 'Y',
	        value: 1e72,
	        scientific: true
	      },
	      R: {
	        name: 'R',
	        value: 1e81,
	        scientific: true
	      },
	      Q: {
	        name: 'Q',
	        value: 1e90,
	        scientific: true
	      },
	      d: {
	        name: 'd',
	        value: 1e-3,
	        scientific: false
	      },
	      c: {
	        name: 'c',
	        value: 1e-6,
	        scientific: false
	      },
	      m: {
	        name: 'm',
	        value: 1e-9,
	        scientific: true
	      },
	      u: {
	        name: 'u',
	        value: 1e-18,
	        scientific: true
	      },
	      n: {
	        name: 'n',
	        value: 1e-27,
	        scientific: true
	      },
	      p: {
	        name: 'p',
	        value: 1e-36,
	        scientific: true
	      },
	      f: {
	        name: 'f',
	        value: 1e-45,
	        scientific: true
	      },
	      a: {
	        name: 'a',
	        value: 1e-54,
	        scientific: true
	      },
	      z: {
	        name: 'z',
	        value: 1e-63,
	        scientific: true
	      },
	      y: {
	        name: 'y',
	        value: 1e-72,
	        scientific: true
	      },
	      r: {
	        name: 'r',
	        value: 1e-81,
	        scientific: true
	      },
	      q: {
	        name: 'q',
	        value: 1e-90,
	        scientific: true
	      }
	    },
	    BINARY_SHORT_SI: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      k: {
	        name: 'k',
	        value: 1e3,
	        scientific: true
	      },
	      M: {
	        name: 'M',
	        value: 1e6,
	        scientific: true
	      },
	      G: {
	        name: 'G',
	        value: 1e9,
	        scientific: true
	      },
	      T: {
	        name: 'T',
	        value: 1e12,
	        scientific: true
	      },
	      P: {
	        name: 'P',
	        value: 1e15,
	        scientific: true
	      },
	      E: {
	        name: 'E',
	        value: 1e18,
	        scientific: true
	      },
	      Z: {
	        name: 'Z',
	        value: 1e21,
	        scientific: true
	      },
	      Y: {
	        name: 'Y',
	        value: 1e24,
	        scientific: true
	      }
	    },
	    BINARY_SHORT_IEC: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      Ki: {
	        name: 'Ki',
	        value: 1024,
	        scientific: true
	      },
	      Mi: {
	        name: 'Mi',
	        value: Math.pow(1024, 2),
	        scientific: true
	      },
	      Gi: {
	        name: 'Gi',
	        value: Math.pow(1024, 3),
	        scientific: true
	      },
	      Ti: {
	        name: 'Ti',
	        value: Math.pow(1024, 4),
	        scientific: true
	      },
	      Pi: {
	        name: 'Pi',
	        value: Math.pow(1024, 5),
	        scientific: true
	      },
	      Ei: {
	        name: 'Ei',
	        value: Math.pow(1024, 6),
	        scientific: true
	      },
	      Zi: {
	        name: 'Zi',
	        value: Math.pow(1024, 7),
	        scientific: true
	      },
	      Yi: {
	        name: 'Yi',
	        value: Math.pow(1024, 8),
	        scientific: true
	      }
	    },
	    BINARY_LONG_SI: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      kilo: {
	        name: 'kilo',
	        value: 1e3,
	        scientific: true
	      },
	      mega: {
	        name: 'mega',
	        value: 1e6,
	        scientific: true
	      },
	      giga: {
	        name: 'giga',
	        value: 1e9,
	        scientific: true
	      },
	      tera: {
	        name: 'tera',
	        value: 1e12,
	        scientific: true
	      },
	      peta: {
	        name: 'peta',
	        value: 1e15,
	        scientific: true
	      },
	      exa: {
	        name: 'exa',
	        value: 1e18,
	        scientific: true
	      },
	      zetta: {
	        name: 'zetta',
	        value: 1e21,
	        scientific: true
	      },
	      yotta: {
	        name: 'yotta',
	        value: 1e24,
	        scientific: true
	      }
	    },
	    BINARY_LONG_IEC: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      kibi: {
	        name: 'kibi',
	        value: 1024,
	        scientific: true
	      },
	      mebi: {
	        name: 'mebi',
	        value: Math.pow(1024, 2),
	        scientific: true
	      },
	      gibi: {
	        name: 'gibi',
	        value: Math.pow(1024, 3),
	        scientific: true
	      },
	      tebi: {
	        name: 'tebi',
	        value: Math.pow(1024, 4),
	        scientific: true
	      },
	      pebi: {
	        name: 'pebi',
	        value: Math.pow(1024, 5),
	        scientific: true
	      },
	      exi: {
	        name: 'exi',
	        value: Math.pow(1024, 6),
	        scientific: true
	      },
	      zebi: {
	        name: 'zebi',
	        value: Math.pow(1024, 7),
	        scientific: true
	      },
	      yobi: {
	        name: 'yobi',
	        value: Math.pow(1024, 8),
	        scientific: true
	      }
	    },
	    BTU: {
	      '': {
	        name: '',
	        value: 1,
	        scientific: true
	      },
	      MM: {
	        name: 'MM',
	        value: 1e6,
	        scientific: true
	      }
	    }
	  };
	  PREFIXES.SHORTLONG = (0, _extends2.default)({}, PREFIXES.SHORT, PREFIXES.LONG);
	  PREFIXES.BINARY_SHORT = (0, _extends2.default)({}, PREFIXES.BINARY_SHORT_SI, PREFIXES.BINARY_SHORT_IEC);
	  PREFIXES.BINARY_LONG = (0, _extends2.default)({}, PREFIXES.BINARY_LONG_SI, PREFIXES.BINARY_LONG_IEC);

	  /* Internally, each unit is represented by a value and a dimension array. The elements of the dimensions array have the following meaning:
	   * Index  Dimension
	   * -----  ---------
	   *   0    Length
	   *   1    Mass
	   *   2    Time
	   *   3    Current
	   *   4    Temperature
	   *   5    Luminous intensity
	   *   6    Amount of substance
	   *   7    Angle
	   *   8    Bit (digital)
	   * For example, the unit "298.15 K" is a pure temperature and would have a value of 298.15 and a dimension array of [0, 0, 0, 0, 1, 0, 0, 0, 0]. The unit "1 cal / (gm °C)" can be written in terms of the 9 fundamental dimensions as [length^2] / ([time^2] * [temperature]), and would a value of (after conversion to SI) 4184.0 and a dimensions array of [2, 0, -2, 0, -1, 0, 0, 0, 0].
	   *
	   */

	  const BASE_DIMENSIONS = ['MASS', 'LENGTH', 'TIME', 'CURRENT', 'TEMPERATURE', 'LUMINOUS_INTENSITY', 'AMOUNT_OF_SUBSTANCE', 'ANGLE', 'BIT'];
	  const BASE_UNITS = {
	    NONE: {
	      dimensions: [0, 0, 0, 0, 0, 0, 0, 0, 0]
	    },
	    MASS: {
	      dimensions: [1, 0, 0, 0, 0, 0, 0, 0, 0]
	    },
	    LENGTH: {
	      dimensions: [0, 1, 0, 0, 0, 0, 0, 0, 0]
	    },
	    TIME: {
	      dimensions: [0, 0, 1, 0, 0, 0, 0, 0, 0]
	    },
	    CURRENT: {
	      dimensions: [0, 0, 0, 1, 0, 0, 0, 0, 0]
	    },
	    TEMPERATURE: {
	      dimensions: [0, 0, 0, 0, 1, 0, 0, 0, 0]
	    },
	    LUMINOUS_INTENSITY: {
	      dimensions: [0, 0, 0, 0, 0, 1, 0, 0, 0]
	    },
	    AMOUNT_OF_SUBSTANCE: {
	      dimensions: [0, 0, 0, 0, 0, 0, 1, 0, 0]
	    },
	    FORCE: {
	      dimensions: [1, 1, -2, 0, 0, 0, 0, 0, 0]
	    },
	    SURFACE: {
	      dimensions: [0, 2, 0, 0, 0, 0, 0, 0, 0]
	    },
	    VOLUME: {
	      dimensions: [0, 3, 0, 0, 0, 0, 0, 0, 0]
	    },
	    ENERGY: {
	      dimensions: [1, 2, -2, 0, 0, 0, 0, 0, 0]
	    },
	    POWER: {
	      dimensions: [1, 2, -3, 0, 0, 0, 0, 0, 0]
	    },
	    PRESSURE: {
	      dimensions: [1, -1, -2, 0, 0, 0, 0, 0, 0]
	    },
	    ELECTRIC_CHARGE: {
	      dimensions: [0, 0, 1, 1, 0, 0, 0, 0, 0]
	    },
	    ELECTRIC_CAPACITANCE: {
	      dimensions: [-1, -2, 4, 2, 0, 0, 0, 0, 0]
	    },
	    ELECTRIC_POTENTIAL: {
	      dimensions: [1, 2, -3, -1, 0, 0, 0, 0, 0]
	    },
	    ELECTRIC_RESISTANCE: {
	      dimensions: [1, 2, -3, -2, 0, 0, 0, 0, 0]
	    },
	    ELECTRIC_INDUCTANCE: {
	      dimensions: [1, 2, -2, -2, 0, 0, 0, 0, 0]
	    },
	    ELECTRIC_CONDUCTANCE: {
	      dimensions: [-1, -2, 3, 2, 0, 0, 0, 0, 0]
	    },
	    MAGNETIC_FLUX: {
	      dimensions: [1, 2, -2, -1, 0, 0, 0, 0, 0]
	    },
	    MAGNETIC_FLUX_DENSITY: {
	      dimensions: [1, 0, -2, -1, 0, 0, 0, 0, 0]
	    },
	    FREQUENCY: {
	      dimensions: [0, 0, -1, 0, 0, 0, 0, 0, 0]
	    },
	    ANGLE: {
	      dimensions: [0, 0, 0, 0, 0, 0, 0, 1, 0]
	    },
	    BIT: {
	      dimensions: [0, 0, 0, 0, 0, 0, 0, 0, 1]
	    }
	  };
	  for (const key in BASE_UNITS) {
	    if ((0, _object.hasOwnProperty)(BASE_UNITS, key)) {
	      BASE_UNITS[key].key = key;
	    }
	  }
	  const BASE_UNIT_NONE = {};
	  const UNIT_NONE = {
	    name: '',
	    base: BASE_UNIT_NONE,
	    value: 1,
	    offset: 0,
	    dimensions: BASE_DIMENSIONS.map(x => 0)
	  };
	  const UNITS = {
	    // length
	    meter: {
	      name: 'meter',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    inch: {
	      name: 'inch',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.0254,
	      offset: 0
	    },
	    foot: {
	      name: 'foot',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.3048,
	      offset: 0
	    },
	    yard: {
	      name: 'yard',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.9144,
	      offset: 0
	    },
	    mile: {
	      name: 'mile',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 1609.344,
	      offset: 0
	    },
	    link: {
	      name: 'link',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.201168,
	      offset: 0
	    },
	    rod: {
	      name: 'rod',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 5.0292,
	      offset: 0
	    },
	    chain: {
	      name: 'chain',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 20.1168,
	      offset: 0
	    },
	    angstrom: {
	      name: 'angstrom',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 1e-10,
	      offset: 0
	    },
	    m: {
	      name: 'm',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    in: {
	      name: 'in',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.0254,
	      offset: 0
	    },
	    ft: {
	      name: 'ft',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.3048,
	      offset: 0
	    },
	    yd: {
	      name: 'yd',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.9144,
	      offset: 0
	    },
	    mi: {
	      name: 'mi',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 1609.344,
	      offset: 0
	    },
	    li: {
	      name: 'li',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.201168,
	      offset: 0
	    },
	    rd: {
	      name: 'rd',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 5.029210,
	      offset: 0
	    },
	    ch: {
	      name: 'ch',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 20.1168,
	      offset: 0
	    },
	    mil: {
	      name: 'mil',
	      base: BASE_UNITS.LENGTH,
	      prefixes: PREFIXES.NONE,
	      value: 0.0000254,
	      offset: 0
	    },
	    // 1/1000 inch

	    // Surface
	    m2: {
	      name: 'm2',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.SQUARED,
	      value: 1,
	      offset: 0
	    },
	    sqin: {
	      name: 'sqin',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 0.00064516,
	      offset: 0
	    },
	    // 645.16 mm2
	    sqft: {
	      name: 'sqft',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 0.09290304,
	      offset: 0
	    },
	    // 0.09290304 m2
	    sqyd: {
	      name: 'sqyd',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 0.83612736,
	      offset: 0
	    },
	    // 0.83612736 m2
	    sqmi: {
	      name: 'sqmi',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 2589988.110336,
	      offset: 0
	    },
	    // 2.589988110336 km2
	    sqrd: {
	      name: 'sqrd',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 25.29295,
	      offset: 0
	    },
	    // 25.29295 m2
	    sqch: {
	      name: 'sqch',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 404.6873,
	      offset: 0
	    },
	    // 404.6873 m2
	    sqmil: {
	      name: 'sqmil',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 6.4516e-10,
	      offset: 0
	    },
	    // 6.4516 * 10^-10 m2
	    acre: {
	      name: 'acre',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 4046.86,
	      offset: 0
	    },
	    // 4046.86 m2
	    hectare: {
	      name: 'hectare',
	      base: BASE_UNITS.SURFACE,
	      prefixes: PREFIXES.NONE,
	      value: 10000,
	      offset: 0
	    },
	    // 10000 m2

	    // Volume
	    m3: {
	      name: 'm3',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.CUBIC,
	      value: 1,
	      offset: 0
	    },
	    L: {
	      name: 'L',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.SHORT,
	      value: 0.001,
	      offset: 0
	    },
	    // litre
	    l: {
	      name: 'l',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.SHORT,
	      value: 0.001,
	      offset: 0
	    },
	    // litre
	    litre: {
	      name: 'litre',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.LONG,
	      value: 0.001,
	      offset: 0
	    },
	    cuin: {
	      name: 'cuin',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 1.6387064e-5,
	      offset: 0
	    },
	    // 1.6387064e-5 m3
	    cuft: {
	      name: 'cuft',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.028316846592,
	      offset: 0
	    },
	    // 28.316 846 592 L
	    cuyd: {
	      name: 'cuyd',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.764554857984,
	      offset: 0
	    },
	    // 764.554 857 984 L
	    teaspoon: {
	      name: 'teaspoon',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.000005,
	      offset: 0
	    },
	    // 5 mL
	    tablespoon: {
	      name: 'tablespoon',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.000015,
	      offset: 0
	    },
	    // 15 mL
	    // {name: 'cup', base: BASE_UNITS.VOLUME, prefixes: PREFIXES.NONE, value: 0.000240, offset: 0}, // 240 mL  // not possible, we have already another cup
	    drop: {
	      name: 'drop',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 5e-8,
	      offset: 0
	    },
	    // 0.05 mL = 5e-8 m3
	    gtt: {
	      name: 'gtt',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 5e-8,
	      offset: 0
	    },
	    // 0.05 mL = 5e-8 m3

	    // Liquid volume
	    minim: {
	      name: 'minim',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.000000061611519921875,
	      offset: 0
	    },
	    // 1/61440 gallons
	    fluiddram: {
	      name: 'fluiddram',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.0000036966911953125,
	      offset: 0
	    },
	    // 1/1024 gallons
	    fluidounce: {
	      name: 'fluidounce',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.0000295735295625,
	      offset: 0
	    },
	    // 1/128 gallons
	    gill: {
	      name: 'gill',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.00011829411825,
	      offset: 0
	    },
	    // 1/32 gallons
	    cc: {
	      name: 'cc',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 1e-6,
	      offset: 0
	    },
	    // 1e-6 L
	    cup: {
	      name: 'cup',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.0002365882365,
	      offset: 0
	    },
	    // 1/16 gallons
	    pint: {
	      name: 'pint',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.000473176473,
	      offset: 0
	    },
	    // 1/8 gallons
	    quart: {
	      name: 'quart',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.000946352946,
	      offset: 0
	    },
	    // 1/4 gallons
	    gallon: {
	      name: 'gallon',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.003785411784,
	      offset: 0
	    },
	    // 3.785411784 L
	    beerbarrel: {
	      name: 'beerbarrel',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.117347765304,
	      offset: 0
	    },
	    // 31 gallons
	    oilbarrel: {
	      name: 'oilbarrel',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.158987294928,
	      offset: 0
	    },
	    // 42 gallons
	    hogshead: {
	      name: 'hogshead',
	      base: BASE_UNITS.VOLUME,
	      prefixes: PREFIXES.NONE,
	      value: 0.238480942392,
	      offset: 0
	    },
	    // 63 gallons

	    // Mass
	    g: {
	      name: 'g',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.SHORT,
	      value: 0.001,
	      offset: 0
	    },
	    gram: {
	      name: 'gram',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.LONG,
	      value: 0.001,
	      offset: 0
	    },
	    ton: {
	      name: 'ton',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.SHORT,
	      value: 907.18474,
	      offset: 0
	    },
	    t: {
	      name: 't',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.SHORT,
	      value: 1000,
	      offset: 0
	    },
	    tonne: {
	      name: 'tonne',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.LONG,
	      value: 1000,
	      offset: 0
	    },
	    grain: {
	      name: 'grain',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 64.79891e-6,
	      offset: 0
	    },
	    dram: {
	      name: 'dram',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 1.7718451953125e-3,
	      offset: 0
	    },
	    ounce: {
	      name: 'ounce',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 28.349523125e-3,
	      offset: 0
	    },
	    poundmass: {
	      name: 'poundmass',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 453.59237e-3,
	      offset: 0
	    },
	    hundredweight: {
	      name: 'hundredweight',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 45.359237,
	      offset: 0
	    },
	    stick: {
	      name: 'stick',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 115e-3,
	      offset: 0
	    },
	    stone: {
	      name: 'stone',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 6.35029318,
	      offset: 0
	    },
	    gr: {
	      name: 'gr',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 64.79891e-6,
	      offset: 0
	    },
	    dr: {
	      name: 'dr',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 1.7718451953125e-3,
	      offset: 0
	    },
	    oz: {
	      name: 'oz',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 28.349523125e-3,
	      offset: 0
	    },
	    lbm: {
	      name: 'lbm',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 453.59237e-3,
	      offset: 0
	    },
	    cwt: {
	      name: 'cwt',
	      base: BASE_UNITS.MASS,
	      prefixes: PREFIXES.NONE,
	      value: 45.359237,
	      offset: 0
	    },
	    // Time
	    s: {
	      name: 's',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    min: {
	      name: 'min',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 60,
	      offset: 0
	    },
	    h: {
	      name: 'h',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 3600,
	      offset: 0
	    },
	    second: {
	      name: 'second',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    sec: {
	      name: 'sec',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    minute: {
	      name: 'minute',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 60,
	      offset: 0
	    },
	    hour: {
	      name: 'hour',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 3600,
	      offset: 0
	    },
	    day: {
	      name: 'day',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 86400,
	      offset: 0
	    },
	    week: {
	      name: 'week',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 7 * 86400,
	      offset: 0
	    },
	    month: {
	      name: 'month',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 2629800,
	      // 1/12th of Julian year
	      offset: 0
	    },
	    year: {
	      name: 'year',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 31557600,
	      // Julian year
	      offset: 0
	    },
	    decade: {
	      name: 'decade',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 315576000,
	      // Julian decade
	      offset: 0
	    },
	    century: {
	      name: 'century',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 3155760000,
	      // Julian century
	      offset: 0
	    },
	    millennium: {
	      name: 'millennium',
	      base: BASE_UNITS.TIME,
	      prefixes: PREFIXES.NONE,
	      value: 31557600000,
	      // Julian millennium
	      offset: 0
	    },
	    // Frequency
	    hertz: {
	      name: 'Hertz',
	      base: BASE_UNITS.FREQUENCY,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0,
	      reciprocal: true
	    },
	    Hz: {
	      name: 'Hz',
	      base: BASE_UNITS.FREQUENCY,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0,
	      reciprocal: true
	    },
	    // Angle
	    rad: {
	      name: 'rad',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    radian: {
	      name: 'radian',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    // deg = rad / (2*pi) * 360 = rad / 0.017453292519943295769236907684888
	    deg: {
	      name: 'deg',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.SHORT,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    degree: {
	      name: 'degree',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.LONG,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    // grad = rad / (2*pi) * 400  = rad / 0.015707963267948966192313216916399
	    grad: {
	      name: 'grad',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.SHORT,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    gradian: {
	      name: 'gradian',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.LONG,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    // cycle = rad / (2*pi) = rad / 6.2831853071795864769252867665793
	    cycle: {
	      name: 'cycle',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.NONE,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    // arcsec = rad / (3600 * (360 / 2 * pi)) = rad / 0.0000048481368110953599358991410235795
	    arcsec: {
	      name: 'arcsec',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.NONE,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    // arcmin = rad / (60 * (360 / 2 * pi)) = rad / 0.00029088820866572159615394846141477
	    arcmin: {
	      name: 'arcmin',
	      base: BASE_UNITS.ANGLE,
	      prefixes: PREFIXES.NONE,
	      value: null,
	      // will be filled in by calculateAngleValues()
	      offset: 0
	    },
	    // Electric current
	    A: {
	      name: 'A',
	      base: BASE_UNITS.CURRENT,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    ampere: {
	      name: 'ampere',
	      base: BASE_UNITS.CURRENT,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    // Temperature
	    // K(C) = °C + 273.15
	    // K(F) = (°F + 459.67) * (5 / 9)
	    // K(R) = °R * (5 / 9)
	    K: {
	      name: 'K',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    degC: {
	      name: 'degC',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 273.15
	    },
	    degF: {
	      name: 'degF',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.SHORT,
	      value: new Fraction(5, 9),
	      offset: 459.67
	    },
	    degR: {
	      name: 'degR',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.SHORT,
	      value: new Fraction(5, 9),
	      offset: 0
	    },
	    kelvin: {
	      name: 'kelvin',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    celsius: {
	      name: 'celsius',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 273.15
	    },
	    fahrenheit: {
	      name: 'fahrenheit',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.LONG,
	      value: new Fraction(5, 9),
	      offset: 459.67
	    },
	    rankine: {
	      name: 'rankine',
	      base: BASE_UNITS.TEMPERATURE,
	      prefixes: PREFIXES.LONG,
	      value: new Fraction(5, 9),
	      offset: 0
	    },
	    // amount of substance
	    mol: {
	      name: 'mol',
	      base: BASE_UNITS.AMOUNT_OF_SUBSTANCE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    mole: {
	      name: 'mole',
	      base: BASE_UNITS.AMOUNT_OF_SUBSTANCE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    // luminous intensity
	    cd: {
	      name: 'cd',
	      base: BASE_UNITS.LUMINOUS_INTENSITY,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    candela: {
	      name: 'candela',
	      base: BASE_UNITS.LUMINOUS_INTENSITY,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    // TODO: units STERADIAN
	    // {name: 'sr', base: BASE_UNITS.STERADIAN, prefixes: PREFIXES.NONE, value: 1, offset: 0},
	    // {name: 'steradian', base: BASE_UNITS.STERADIAN, prefixes: PREFIXES.NONE, value: 1, offset: 0},

	    // Force
	    N: {
	      name: 'N',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    newton: {
	      name: 'newton',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    dyn: {
	      name: 'dyn',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.SHORT,
	      value: 0.00001,
	      offset: 0
	    },
	    dyne: {
	      name: 'dyne',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.LONG,
	      value: 0.00001,
	      offset: 0
	    },
	    lbf: {
	      name: 'lbf',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.NONE,
	      value: 4.4482216152605,
	      offset: 0
	    },
	    poundforce: {
	      name: 'poundforce',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.NONE,
	      value: 4.4482216152605,
	      offset: 0
	    },
	    kip: {
	      name: 'kip',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.LONG,
	      value: 4448.2216,
	      offset: 0
	    },
	    kilogramforce: {
	      name: 'kilogramforce',
	      base: BASE_UNITS.FORCE,
	      prefixes: PREFIXES.NONE,
	      value: 9.80665,
	      offset: 0
	    },
	    // Energy
	    J: {
	      name: 'J',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    joule: {
	      name: 'joule',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    erg: {
	      name: 'erg',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.SHORTLONG,
	      // Both kiloerg and kerg are acceptable
	      value: 1e-7,
	      offset: 0
	    },
	    Wh: {
	      name: 'Wh',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.SHORT,
	      value: 3600,
	      offset: 0
	    },
	    BTU: {
	      name: 'BTU',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.BTU,
	      value: 1055.05585262,
	      offset: 0
	    },
	    eV: {
	      name: 'eV',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.SHORT,
	      value: 1.602176565e-19,
	      offset: 0
	    },
	    electronvolt: {
	      name: 'electronvolt',
	      base: BASE_UNITS.ENERGY,
	      prefixes: PREFIXES.LONG,
	      value: 1.602176565e-19,
	      offset: 0
	    },
	    // Power
	    W: {
	      name: 'W',
	      base: BASE_UNITS.POWER,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    watt: {
	      name: 'watt',
	      base: BASE_UNITS.POWER,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    hp: {
	      name: 'hp',
	      base: BASE_UNITS.POWER,
	      prefixes: PREFIXES.NONE,
	      value: 745.6998715386,
	      offset: 0
	    },
	    // Electrical power units
	    VAR: {
	      name: 'VAR',
	      base: BASE_UNITS.POWER,
	      prefixes: PREFIXES.SHORT,
	      value: Complex.I,
	      offset: 0
	    },
	    VA: {
	      name: 'VA',
	      base: BASE_UNITS.POWER,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Pressure
	    Pa: {
	      name: 'Pa',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    psi: {
	      name: 'psi',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.NONE,
	      value: 6894.75729276459,
	      offset: 0
	    },
	    atm: {
	      name: 'atm',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.NONE,
	      value: 101325,
	      offset: 0
	    },
	    bar: {
	      name: 'bar',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.SHORTLONG,
	      value: 100000,
	      offset: 0
	    },
	    torr: {
	      name: 'torr',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.NONE,
	      value: 133.322,
	      offset: 0
	    },
	    mmHg: {
	      name: 'mmHg',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.NONE,
	      value: 133.322,
	      offset: 0
	    },
	    mmH2O: {
	      name: 'mmH2O',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.NONE,
	      value: 9.80665,
	      offset: 0
	    },
	    cmH2O: {
	      name: 'cmH2O',
	      base: BASE_UNITS.PRESSURE,
	      prefixes: PREFIXES.NONE,
	      value: 98.0665,
	      offset: 0
	    },
	    // Electric charge
	    coulomb: {
	      name: 'coulomb',
	      base: BASE_UNITS.ELECTRIC_CHARGE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    C: {
	      name: 'C',
	      base: BASE_UNITS.ELECTRIC_CHARGE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    Ah: {
	      name: 'Ah',
	      base: BASE_UNITS.ELECTRIC_CHARGE,
	      prefixes: PREFIXES.SHORT,
	      value: 3600,
	      offset: 0
	    },
	    // Electric capacitance
	    farad: {
	      name: 'farad',
	      base: BASE_UNITS.ELECTRIC_CAPACITANCE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    F: {
	      name: 'F',
	      base: BASE_UNITS.ELECTRIC_CAPACITANCE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Electric potential
	    volt: {
	      name: 'volt',
	      base: BASE_UNITS.ELECTRIC_POTENTIAL,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    V: {
	      name: 'V',
	      base: BASE_UNITS.ELECTRIC_POTENTIAL,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Electric resistance
	    ohm: {
	      name: 'ohm',
	      base: BASE_UNITS.ELECTRIC_RESISTANCE,
	      prefixes: PREFIXES.SHORTLONG,
	      // Both Mohm and megaohm are acceptable
	      value: 1,
	      offset: 0
	    },
	    /*
	     * Unicode breaks in browsers if charset is not specified
	    Ω: {
	      name: 'Ω',
	      base: BASE_UNITS.ELECTRIC_RESISTANCE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    */
	    // Electric inductance
	    henry: {
	      name: 'henry',
	      base: BASE_UNITS.ELECTRIC_INDUCTANCE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    H: {
	      name: 'H',
	      base: BASE_UNITS.ELECTRIC_INDUCTANCE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Electric conductance
	    siemens: {
	      name: 'siemens',
	      base: BASE_UNITS.ELECTRIC_CONDUCTANCE,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    S: {
	      name: 'S',
	      base: BASE_UNITS.ELECTRIC_CONDUCTANCE,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Magnetic flux
	    weber: {
	      name: 'weber',
	      base: BASE_UNITS.MAGNETIC_FLUX,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    Wb: {
	      name: 'Wb',
	      base: BASE_UNITS.MAGNETIC_FLUX,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Magnetic flux density
	    tesla: {
	      name: 'tesla',
	      base: BASE_UNITS.MAGNETIC_FLUX_DENSITY,
	      prefixes: PREFIXES.LONG,
	      value: 1,
	      offset: 0
	    },
	    T: {
	      name: 'T',
	      base: BASE_UNITS.MAGNETIC_FLUX_DENSITY,
	      prefixes: PREFIXES.SHORT,
	      value: 1,
	      offset: 0
	    },
	    // Binary
	    b: {
	      name: 'b',
	      base: BASE_UNITS.BIT,
	      prefixes: PREFIXES.BINARY_SHORT,
	      value: 1,
	      offset: 0
	    },
	    bits: {
	      name: 'bits',
	      base: BASE_UNITS.BIT,
	      prefixes: PREFIXES.BINARY_LONG,
	      value: 1,
	      offset: 0
	    },
	    B: {
	      name: 'B',
	      base: BASE_UNITS.BIT,
	      prefixes: PREFIXES.BINARY_SHORT,
	      value: 8,
	      offset: 0
	    },
	    bytes: {
	      name: 'bytes',
	      base: BASE_UNITS.BIT,
	      prefixes: PREFIXES.BINARY_LONG,
	      value: 8,
	      offset: 0
	    }
	  };

	  // aliases (formerly plurals)
	  // note that ALIASES is only used at creation to create more entries in UNITS by copying the aliased units
	  const ALIASES = {
	    meters: 'meter',
	    inches: 'inch',
	    feet: 'foot',
	    yards: 'yard',
	    miles: 'mile',
	    links: 'link',
	    rods: 'rod',
	    chains: 'chain',
	    angstroms: 'angstrom',
	    lt: 'l',
	    litres: 'litre',
	    liter: 'litre',
	    liters: 'litre',
	    teaspoons: 'teaspoon',
	    tablespoons: 'tablespoon',
	    minims: 'minim',
	    fldr: 'fluiddram',
	    fluiddrams: 'fluiddram',
	    floz: 'fluidounce',
	    fluidounces: 'fluidounce',
	    gi: 'gill',
	    gills: 'gill',
	    cp: 'cup',
	    cups: 'cup',
	    pt: 'pint',
	    pints: 'pint',
	    qt: 'quart',
	    quarts: 'quart',
	    gal: 'gallon',
	    gallons: 'gallon',
	    bbl: 'beerbarrel',
	    beerbarrels: 'beerbarrel',
	    obl: 'oilbarrel',
	    oilbarrels: 'oilbarrel',
	    hogsheads: 'hogshead',
	    gtts: 'gtt',
	    grams: 'gram',
	    tons: 'ton',
	    tonnes: 'tonne',
	    grains: 'grain',
	    drams: 'dram',
	    ounces: 'ounce',
	    poundmasses: 'poundmass',
	    hundredweights: 'hundredweight',
	    sticks: 'stick',
	    lb: 'lbm',
	    lbs: 'lbm',
	    kips: 'kip',
	    kgf: 'kilogramforce',
	    acres: 'acre',
	    hectares: 'hectare',
	    sqfeet: 'sqft',
	    sqyard: 'sqyd',
	    sqmile: 'sqmi',
	    sqmiles: 'sqmi',
	    mmhg: 'mmHg',
	    mmh2o: 'mmH2O',
	    cmh2o: 'cmH2O',
	    seconds: 'second',
	    secs: 'second',
	    minutes: 'minute',
	    mins: 'minute',
	    hours: 'hour',
	    hr: 'hour',
	    hrs: 'hour',
	    days: 'day',
	    weeks: 'week',
	    months: 'month',
	    years: 'year',
	    decades: 'decade',
	    centuries: 'century',
	    millennia: 'millennium',
	    hertz: 'hertz',
	    radians: 'radian',
	    degrees: 'degree',
	    gradians: 'gradian',
	    cycles: 'cycle',
	    arcsecond: 'arcsec',
	    arcseconds: 'arcsec',
	    arcminute: 'arcmin',
	    arcminutes: 'arcmin',
	    BTUs: 'BTU',
	    watts: 'watt',
	    joules: 'joule',
	    amperes: 'ampere',
	    amps: 'ampere',
	    amp: 'ampere',
	    coulombs: 'coulomb',
	    volts: 'volt',
	    ohms: 'ohm',
	    farads: 'farad',
	    webers: 'weber',
	    teslas: 'tesla',
	    electronvolts: 'electronvolt',
	    moles: 'mole',
	    bit: 'bits',
	    byte: 'bytes'
	  };

	  /**
	   * Calculate the values for the angle units.
	   * Value is calculated as number or BigNumber depending on the configuration
	   * @param {{number: 'number' | 'BigNumber'}} config
	   */
	  function calculateAngleValues(config) {
	    if (config.number === 'BigNumber') {
	      const pi = (0, _constants.createBigNumberPi)(BigNumber);
	      UNITS.rad.value = new BigNumber(1);
	      UNITS.deg.value = pi.div(180); // 2 * pi / 360
	      UNITS.grad.value = pi.div(200); // 2 * pi / 400
	      UNITS.cycle.value = pi.times(2); // 2 * pi
	      UNITS.arcsec.value = pi.div(648000); // 2 * pi / 360 / 3600
	      UNITS.arcmin.value = pi.div(10800); // 2 * pi / 360 / 60
	    } else {
	      // number
	      UNITS.rad.value = 1;
	      UNITS.deg.value = Math.PI / 180; // 2 * pi / 360
	      UNITS.grad.value = Math.PI / 200; // 2 * pi / 400
	      UNITS.cycle.value = Math.PI * 2; // 2 * pi
	      UNITS.arcsec.value = Math.PI / 648000; // 2 * pi / 360 / 3600
	      UNITS.arcmin.value = Math.PI / 10800; // 2 * pi / 360 / 60
	    }

	    // copy to the full names of the angles
	    UNITS.radian.value = UNITS.rad.value;
	    UNITS.degree.value = UNITS.deg.value;
	    UNITS.gradian.value = UNITS.grad.value;
	  }

	  // apply the angle values now
	  calculateAngleValues(config);
	  if (on) {
	    // recalculate the values on change of configuration
	    on('config', function (curr, prev) {
	      if (curr.number !== prev.number) {
	        calculateAngleValues(curr);
	      }
	    });
	  }

	  /**
	   * A unit system is a set of dimensionally independent base units plus a set of derived units, formed by multiplication and division of the base units, that are by convention used with the unit system.
	   * A user perhaps could issue a command to select a preferred unit system, or use the default (see below).
	   * Auto unit system: The default unit system is updated on the fly anytime a unit is parsed. The corresponding unit in the default unit system is updated, so that answers are given in the same units the user supplies.
	   */
	  const UNIT_SYSTEMS = {
	    si: {
	      // Base units
	      NONE: {
	        unit: UNIT_NONE,
	        prefix: PREFIXES.NONE['']
	      },
	      LENGTH: {
	        unit: UNITS.m,
	        prefix: PREFIXES.SHORT['']
	      },
	      MASS: {
	        unit: UNITS.g,
	        prefix: PREFIXES.SHORT.k
	      },
	      TIME: {
	        unit: UNITS.s,
	        prefix: PREFIXES.SHORT['']
	      },
	      CURRENT: {
	        unit: UNITS.A,
	        prefix: PREFIXES.SHORT['']
	      },
	      TEMPERATURE: {
	        unit: UNITS.K,
	        prefix: PREFIXES.SHORT['']
	      },
	      LUMINOUS_INTENSITY: {
	        unit: UNITS.cd,
	        prefix: PREFIXES.SHORT['']
	      },
	      AMOUNT_OF_SUBSTANCE: {
	        unit: UNITS.mol,
	        prefix: PREFIXES.SHORT['']
	      },
	      ANGLE: {
	        unit: UNITS.rad,
	        prefix: PREFIXES.SHORT['']
	      },
	      BIT: {
	        unit: UNITS.bits,
	        prefix: PREFIXES.SHORT['']
	      },
	      // Derived units
	      FORCE: {
	        unit: UNITS.N,
	        prefix: PREFIXES.SHORT['']
	      },
	      ENERGY: {
	        unit: UNITS.J,
	        prefix: PREFIXES.SHORT['']
	      },
	      POWER: {
	        unit: UNITS.W,
	        prefix: PREFIXES.SHORT['']
	      },
	      PRESSURE: {
	        unit: UNITS.Pa,
	        prefix: PREFIXES.SHORT['']
	      },
	      ELECTRIC_CHARGE: {
	        unit: UNITS.C,
	        prefix: PREFIXES.SHORT['']
	      },
	      ELECTRIC_CAPACITANCE: {
	        unit: UNITS.F,
	        prefix: PREFIXES.SHORT['']
	      },
	      ELECTRIC_POTENTIAL: {
	        unit: UNITS.V,
	        prefix: PREFIXES.SHORT['']
	      },
	      ELECTRIC_RESISTANCE: {
	        unit: UNITS.ohm,
	        prefix: PREFIXES.SHORT['']
	      },
	      ELECTRIC_INDUCTANCE: {
	        unit: UNITS.H,
	        prefix: PREFIXES.SHORT['']
	      },
	      ELECTRIC_CONDUCTANCE: {
	        unit: UNITS.S,
	        prefix: PREFIXES.SHORT['']
	      },
	      MAGNETIC_FLUX: {
	        unit: UNITS.Wb,
	        prefix: PREFIXES.SHORT['']
	      },
	      MAGNETIC_FLUX_DENSITY: {
	        unit: UNITS.T,
	        prefix: PREFIXES.SHORT['']
	      },
	      FREQUENCY: {
	        unit: UNITS.Hz,
	        prefix: PREFIXES.SHORT['']
	      }
	    }
	  };

	  // Clone to create the other unit systems
	  UNIT_SYSTEMS.cgs = JSON.parse(JSON.stringify(UNIT_SYSTEMS.si));
	  UNIT_SYSTEMS.cgs.LENGTH = {
	    unit: UNITS.m,
	    prefix: PREFIXES.SHORT.c
	  };
	  UNIT_SYSTEMS.cgs.MASS = {
	    unit: UNITS.g,
	    prefix: PREFIXES.SHORT['']
	  };
	  UNIT_SYSTEMS.cgs.FORCE = {
	    unit: UNITS.dyn,
	    prefix: PREFIXES.SHORT['']
	  };
	  UNIT_SYSTEMS.cgs.ENERGY = {
	    unit: UNITS.erg,
	    prefix: PREFIXES.NONE['']
	  };
	  // there are wholly 4 unique cgs systems for electricity and magnetism,
	  // so let's not worry about it unless somebody complains

	  UNIT_SYSTEMS.us = JSON.parse(JSON.stringify(UNIT_SYSTEMS.si));
	  UNIT_SYSTEMS.us.LENGTH = {
	    unit: UNITS.ft,
	    prefix: PREFIXES.NONE['']
	  };
	  UNIT_SYSTEMS.us.MASS = {
	    unit: UNITS.lbm,
	    prefix: PREFIXES.NONE['']
	  };
	  UNIT_SYSTEMS.us.TEMPERATURE = {
	    unit: UNITS.degF,
	    prefix: PREFIXES.NONE['']
	  };
	  UNIT_SYSTEMS.us.FORCE = {
	    unit: UNITS.lbf,
	    prefix: PREFIXES.NONE['']
	  };
	  UNIT_SYSTEMS.us.ENERGY = {
	    unit: UNITS.BTU,
	    prefix: PREFIXES.BTU['']
	  };
	  UNIT_SYSTEMS.us.POWER = {
	    unit: UNITS.hp,
	    prefix: PREFIXES.NONE['']
	  };
	  UNIT_SYSTEMS.us.PRESSURE = {
	    unit: UNITS.psi,
	    prefix: PREFIXES.NONE['']
	  };

	  // Add additional unit systems here.

	  // Choose a unit system to seed the auto unit system.
	  UNIT_SYSTEMS.auto = JSON.parse(JSON.stringify(UNIT_SYSTEMS.si));

	  // Set the current unit system
	  let currentUnitSystem = UNIT_SYSTEMS.auto;

	  /**
	   * Set a unit system for formatting derived units.
	   * @memberof Unit
	   * @param {string} [name] The name of the unit system.
	   */
	  Unit.setUnitSystem = function (name) {
	    if ((0, _object.hasOwnProperty)(UNIT_SYSTEMS, name)) {
	      currentUnitSystem = UNIT_SYSTEMS[name];
	    } else {
	      throw new Error('Unit system ' + name + ' does not exist. Choices are: ' + Object.keys(UNIT_SYSTEMS).join(', '));
	    }
	  };

	  /**
	   * Return the current unit system.
	   * @memberof Unit
	   * @return {string} The current unit system.
	   */
	  Unit.getUnitSystem = function () {
	    for (const key in UNIT_SYSTEMS) {
	      if ((0, _object.hasOwnProperty)(UNIT_SYSTEMS, key)) {
	        if (UNIT_SYSTEMS[key] === currentUnitSystem) {
	          return key;
	        }
	      }
	    }
	  };

	  /**
	   * Converters to convert from number to an other numeric type like BigNumber
	   * or Fraction
	   */
	  Unit.typeConverters = {
	    BigNumber: function (x) {
	      if (x !== null && x !== void 0 && x.isFraction) return new BigNumber(String(x.n)).div(String(x.d)).times(String(x.s));
	      return new BigNumber(x + ''); // stringify to prevent constructor error
	    },
	    Fraction: function (x) {
	      return new Fraction(x);
	    },
	    Complex: function (x) {
	      return x;
	    },
	    number: function (x) {
	      if (x !== null && x !== void 0 && x.isFraction) return number(x);
	      return x;
	    }
	  };

	  /**
	   * Retrieve the right converter function corresponding with this unit's
	   * value
	   *
	   * @memberof Unit
	   * @return {Function}
	   */
	  Unit.prototype._numberConverter = function () {
	    const convert = Unit.typeConverters[this.valueType()];
	    if (convert) {
	      return convert;
	    }
	    throw new TypeError('Unsupported Unit value type "' + this.valueType() + '"');
	  };

	  /**
	   * Retrieve the right convertor function corresponding with the type
	   * of provided exampleValue.
	   *
	   * @param {string} type   A string 'number', 'BigNumber', or 'Fraction'
	   *                        In case of an unknown type,
	   * @return {Function}
	   */
	  Unit._getNumberConverter = function (type) {
	    if (!Unit.typeConverters[type]) {
	      throw new TypeError('Unsupported type "' + type + '"');
	    }
	    return Unit.typeConverters[type];
	  };

	  // Add dimensions to each built-in unit
	  for (const key in UNITS) {
	    if ((0, _object.hasOwnProperty)(UNITS, key)) {
	      const unit = UNITS[key];
	      unit.dimensions = unit.base.dimensions;
	    }
	  }

	  // Create aliases
	  for (const name in ALIASES) {
	    if ((0, _object.hasOwnProperty)(ALIASES, name)) {
	      const unit = UNITS[ALIASES[name]];
	      const alias = {};
	      for (const key in unit) {
	        if ((0, _object.hasOwnProperty)(unit, key)) {
	          alias[key] = unit[key];
	        }
	      }
	      alias.name = name;
	      UNITS[name] = alias;
	    }
	  }

	  /**
	   * Checks if a character is a valid latin letter (upper or lower case).
	   * Note that this function can be overridden, for example to allow support of other alphabets.
	   * @memberof Unit
	   * @param {string} c Tested character
	   * @return {boolean} true if the character is a latin letter
	   */
	  Unit.isValidAlpha = function isValidAlpha(c) {
	    return /^[a-zA-Z]$/.test(c);
	  };
	  function assertUnitNameIsValid(name) {
	    for (let i = 0; i < name.length; i++) {
	      c = name.charAt(i);
	      if (i === 0 && !Unit.isValidAlpha(c)) {
	        throw new Error('Invalid unit name (must begin with alpha character): "' + name + '"');
	      }
	      if (i > 0 && !(Unit.isValidAlpha(c) || isDigit(c))) {
	        throw new Error('Invalid unit name (only alphanumeric characters are allowed): "' + name + '"');
	      }
	    }
	  }

	  /**
	   * Wrapper around createUnitSingle.
	   * Example:
	   *  createUnit( {
	   *     foo: {
	   *       prefixes: 'long',
	   *       baseName: 'essence-of-foo'
	   *     },
	   *     bar: '40 foo',
	   *     baz: {
	   *       definition: '1 bar/hour',
	   *       prefixes: 'long'
	   *     }
	   *   },
	   *   {
	   *     override: true
	   *   })
	   * @memberof Unit
	   * @param {object} obj      Object map. Each key becomes a unit which is defined by its value.
	   * @param {object} options
	   * @return {Unit} the last created unit
	   */
	  Unit.createUnit = function (obj, options) {
	    if (typeof obj !== 'object') {
	      throw new TypeError("createUnit expects first parameter to be of type 'Object'");
	    }

	    // Remove all units and aliases we are overriding
	    if (options && options.override) {
	      for (const key in obj) {
	        if ((0, _object.hasOwnProperty)(obj, key)) {
	          Unit.deleteUnit(key);
	        }
	        if (obj[key].aliases) {
	          for (let i = 0; i < obj[key].aliases.length; i++) {
	            Unit.deleteUnit(obj[key].aliases[i]);
	          }
	        }
	      }
	    }

	    // TODO: traverse multiple times until all units have been added
	    let lastUnit;
	    for (const key in obj) {
	      if ((0, _object.hasOwnProperty)(obj, key)) {
	        lastUnit = Unit.createUnitSingle(key, obj[key]);
	      }
	    }
	    return lastUnit;
	  };

	  /**
	   * Create a user-defined unit and register it with the Unit type.
	   * Example:
	   *  createUnitSingle('knot', '0.514444444 m/s')
	   *
	   * @memberof Unit
	   * @param {string} name      The name of the new unit. Must be unique. Example: 'knot'
	   * @param {string | Unit | object} definition      Definition of the unit in terms
	   * of existing units. For example, '0.514444444 m / s'. Can be a Unit, a string,
	   * or an Object. If an Object, may have the following properties:
	   *   - definition {string | Unit} The definition of this unit.
	   *   - prefixes {string} "none", "short", "long", "binary_short", or "binary_long".
	   *     The default is "none".
	   *   - aliases {Array} Array of strings. Example: ['knots', 'kt', 'kts']
	   *   - offset {Numeric} An offset to apply when converting from the unit. For
	   *     example, the offset for celsius is 273.15 and the offset for farhenheit
	   *     is 459.67. Default is 0.
	   *   - baseName {string} If the unit's dimension does not match that of any other
	   *     base unit, the name of the newly create base unit. Otherwise, this property
	   *     has no effect.
	   *
	   * @return {Unit}
	   */
	  Unit.createUnitSingle = function (name, obj) {
	    if (typeof obj === 'undefined' || obj === null) {
	      obj = {};
	    }
	    if (typeof name !== 'string') {
	      throw new TypeError("createUnitSingle expects first parameter to be of type 'string'");
	    }

	    // Check collisions with existing units
	    if ((0, _object.hasOwnProperty)(UNITS, name)) {
	      throw new Error('Cannot create unit "' + name + '": a unit with that name already exists');
	    }

	    // TODO: Validate name for collisions with other built-in functions (like abs or cos, for example), and for acceptable variable names. For example, '42' is probably not a valid unit. Nor is '%', since it is also an operator.

	    assertUnitNameIsValid(name);
	    let defUnit = null; // The Unit from which the new unit will be created.
	    let aliases = [];
	    let offset = 0;
	    let definition;
	    let prefixes;
	    let baseName;
	    if (obj && obj.type === 'Unit') {
	      defUnit = obj.clone();
	    } else if (typeof obj === 'string') {
	      if (obj !== '') {
	        definition = obj;
	      }
	    } else if (typeof obj === 'object') {
	      definition = obj.definition;
	      prefixes = obj.prefixes;
	      offset = obj.offset;
	      baseName = obj.baseName;
	      if (obj.aliases) {
	        aliases = obj.aliases.valueOf(); // aliases could be a Matrix, so convert to Array
	      }
	    } else {
	      throw new TypeError('Cannot create unit "' + name + '" from "' + obj.toString() + '": expecting "string" or "Unit" or "Object"');
	    }
	    if (aliases) {
	      for (let i = 0; i < aliases.length; i++) {
	        if ((0, _object.hasOwnProperty)(UNITS, aliases[i])) {
	          throw new Error('Cannot create alias "' + aliases[i] + '": a unit with that name already exists');
	        }
	      }
	    }
	    if (definition && typeof definition === 'string' && !defUnit) {
	      try {
	        defUnit = Unit.parse(definition, {
	          allowNoUnits: true
	        });
	      } catch (ex) {
	        ex.message = 'Could not create unit "' + name + '" from "' + definition + '": ' + ex.message;
	        throw ex;
	      }
	    } else if (definition && definition.type === 'Unit') {
	      defUnit = definition.clone();
	    }
	    aliases = aliases || [];
	    offset = offset || 0;
	    if (prefixes && prefixes.toUpperCase) {
	      prefixes = PREFIXES[prefixes.toUpperCase()] || PREFIXES.NONE;
	    } else {
	      prefixes = PREFIXES.NONE;
	    }

	    // If defUnit is null, it is because the user did not
	    // specify a defintion. So create a new base dimension.
	    let newUnit = {};
	    if (!defUnit) {
	      // Add a new base dimension
	      baseName = baseName || name + '_STUFF'; // foo --> foo_STUFF, or the essence of foo
	      if (BASE_DIMENSIONS.indexOf(baseName) >= 0) {
	        throw new Error('Cannot create new base unit "' + name + '": a base unit with that name already exists (and cannot be overridden)');
	      }
	      BASE_DIMENSIONS.push(baseName);

	      // Push 0 onto existing base units
	      for (const b in BASE_UNITS) {
	        if ((0, _object.hasOwnProperty)(BASE_UNITS, b)) {
	          BASE_UNITS[b].dimensions[BASE_DIMENSIONS.length - 1] = 0;
	        }
	      }

	      // Add the new base unit
	      const newBaseUnit = {
	        dimensions: []
	      };
	      for (let i = 0; i < BASE_DIMENSIONS.length; i++) {
	        newBaseUnit.dimensions[i] = 0;
	      }
	      newBaseUnit.dimensions[BASE_DIMENSIONS.length - 1] = 1;
	      newBaseUnit.key = baseName;
	      BASE_UNITS[baseName] = newBaseUnit;
	      newUnit = {
	        name,
	        value: 1,
	        dimensions: BASE_UNITS[baseName].dimensions.slice(0),
	        prefixes,
	        offset,
	        base: BASE_UNITS[baseName]
	      };
	      currentUnitSystem[baseName] = {
	        unit: newUnit,
	        prefix: PREFIXES.NONE['']
	      };
	    } else {
	      newUnit = {
	        name,
	        value: defUnit.value,
	        dimensions: defUnit.dimensions.slice(0),
	        prefixes,
	        offset
	      };

	      // Create a new base if no matching base exists
	      let anyMatch = false;
	      for (const i in BASE_UNITS) {
	        if ((0, _object.hasOwnProperty)(BASE_UNITS, i)) {
	          let match = true;
	          for (let j = 0; j < BASE_DIMENSIONS.length; j++) {
	            if (Math.abs((newUnit.dimensions[j] || 0) - (BASE_UNITS[i].dimensions[j] || 0)) > 1e-12) {
	              match = false;
	              break;
	            }
	          }
	          if (match) {
	            anyMatch = true;
	            newUnit.base = BASE_UNITS[i];
	            break;
	          }
	        }
	      }
	      if (!anyMatch) {
	        baseName = baseName || name + '_STUFF'; // foo --> foo_STUFF, or the essence of foo
	        // Add the new base unit
	        const newBaseUnit = {
	          dimensions: defUnit.dimensions.slice(0)
	        };
	        newBaseUnit.key = baseName;
	        BASE_UNITS[baseName] = newBaseUnit;
	        currentUnitSystem[baseName] = {
	          unit: newUnit,
	          prefix: PREFIXES.NONE['']
	        };
	        newUnit.base = BASE_UNITS[baseName];
	      }
	    }
	    Unit.UNITS[name] = newUnit;
	    for (let i = 0; i < aliases.length; i++) {
	      const aliasName = aliases[i];
	      const alias = {};
	      for (const key in newUnit) {
	        if ((0, _object.hasOwnProperty)(newUnit, key)) {
	          alias[key] = newUnit[key];
	        }
	      }
	      alias.name = aliasName;
	      Unit.UNITS[aliasName] = alias;
	    }

	    // delete the memoization cache because we created a new unit
	    delete _findUnit.cache;
	    return new Unit(null, name);
	  };
	  Unit.deleteUnit = function (name) {
	    delete Unit.UNITS[name];

	    // delete the memoization cache because we deleted a unit
	    delete _findUnit.cache;
	  };

	  // expose arrays with prefixes, dimensions, units, systems
	  Unit.PREFIXES = PREFIXES;
	  Unit.BASE_DIMENSIONS = BASE_DIMENSIONS;
	  Unit.BASE_UNITS = BASE_UNITS;
	  Unit.UNIT_SYSTEMS = UNIT_SYSTEMS;
	  Unit.UNITS = UNITS;
	  return Unit;
	}, {
	  isClass: true
	});
	return Unit;
}

var unit$1 = {};

var hasRequiredUnit$1;

function requireUnit$1 () {
	if (hasRequiredUnit$1) return unit$1;
	hasRequiredUnit$1 = 1;

	Object.defineProperty(unit$1, "__esModule", {
	  value: true
	});
	unit$1.createUnitFunction = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'unit';
	const dependencies = ['typed', 'Unit'];

	// This function is named createUnitFunction to prevent a naming conflict with createUnit
	unit$1.createUnitFunction = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Unit
	  } = _ref;
	  /**
	   * Create a unit. Depending on the passed arguments, the function
	   * will create and return a new math.Unit object.
	   * When a matrix is provided, all elements will be converted to units.
	   *
	   * Syntax:
	   *
	   *     math.unit(unit : string)
	   *     math.unit(value : number, valuelessUnit : Unit)
	   *     math.unit(value : number, valuelessUnit : string)
	   *
	   * Examples:
	   *
	   *    const kph = math.unit('km/h')   // returns Unit km/h (valueless)
	   *    const v = math.unit(25, kph)    // returns Unit 25 km/h
	   *    const a = math.unit(5, 'cm')    // returns Unit 50 mm
	   *    const b = math.unit('23 kg')    // returns Unit 23 kg
	   *    a.to('m')                       // returns Unit 0.05 m
	   *
	   * See also:
	   *
	   *    bignumber, boolean, complex, index, matrix, number, string, createUnit
	   *
	   * History:
	   *
	   *    v0.5   Created
	   *    v0.16  Support conversion from BigNumber
	   *    v2.5   Support BigNumber and Fraction values in units
	   *    v2.6   Support Complex values in units
	   *    v11.1  Allow the type of unit to be specifed by a unit (not just string)
	   *
	   * @param {* | Array | Matrix} args   A number and unit.
	   * @return {Unit | Array | Matrix}    The created unit
	   */

	  return typed(name, {
	    Unit: function (x) {
	      return x.clone();
	    },
	    string: function (x) {
	      if (Unit.isValuelessUnit(x)) {
	        return new Unit(null, x); // a pure unit
	      }
	      return Unit.parse(x, {
	        allowNoUnits: true
	      }); // a unit with value, like '5cm'
	    },
	    'number | BigNumber | Fraction | Complex, string | Unit': function (value, unit) {
	      return new Unit(value, unit);
	    },
	    'number | BigNumber | Fraction': function (value) {
	      // dimensionless
	      return new Unit(value);
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return unit$1;
}

var sparse$1 = {};

var hasRequiredSparse$1;

function requireSparse$1 () {
	if (hasRequiredSparse$1) return sparse$1;
	hasRequiredSparse$1 = 1;

	Object.defineProperty(sparse$1, "__esModule", {
	  value: true
	});
	sparse$1.createSparse = void 0;
	var _factory = requireFactory$b();
	const name = 'sparse';
	const dependencies = ['typed', 'SparseMatrix'];
	sparse$1.createSparse = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    SparseMatrix
	  } = _ref;
	  /**
	   * Create a Sparse Matrix. The function creates a new `math.Matrix` object from
	   * an `Array`. A Matrix has utility functions to manipulate the data in the
	   * matrix, like getting the size and getting or setting values in the matrix.
	   * Note that a Sparse Matrix is always 2-dimensional, so for example if
	   * you create one from a plain array of _n_ numbers, you get an _n_ by 1
	   * Sparse "column vector".
	   *
	   * Syntax:
	   *
	   *    math.sparse()               // creates an empty sparse matrix.
	   *    math.sparse(data)           // creates a sparse matrix with initial data.
	   *    math.sparse(data, 'number') // creates a sparse matrix with initial data, number datatype.
	   *
	   * Examples:
	   *
	   *    let m = math.sparse([[1, 2], [3, 4]])
	   *    m.size()                        // Array [2, 2]
	   *    m.resize([3, 2], 5)
	   *    m.valueOf()                     // Array [[1, 2], [3, 4], [5, 5]]
	   *    m.get([1, 0])                    // number 3
	   *    let v = math.sparse([0, 0, 1])
	   *    v.size()                        // Array [3, 1]
	   *    v.get([2, 0])                   // number 1
	   *
	   * See also:
	   *
	   *    bignumber, boolean, complex, index, number, string, unit, matrix
	   *
	   * History:
	   *
	   *    v1.5  Created
	   *
	   * @param {Array | Matrix} [data]    A two dimensional array
	   *
	   * @return {Matrix} The created matrix
	   */
	  return typed(name, {
	    '': function () {
	      return new SparseMatrix([]);
	    },
	    string: function (datatype) {
	      return new SparseMatrix([], datatype);
	    },
	    'Array | Matrix': function (data) {
	      return new SparseMatrix(data);
	    },
	    'Array | Matrix, string': function (data, datatype) {
	      return new SparseMatrix(data, datatype);
	    }
	  });
	});
	return sparse$1;
}

var createUnit$1 = {};

var hasRequiredCreateUnit$1;

function requireCreateUnit$1 () {
	if (hasRequiredCreateUnit$1) return createUnit$1;
	hasRequiredCreateUnit$1 = 1;

	Object.defineProperty(createUnit$1, "__esModule", {
	  value: true
	});
	createUnit$1.createCreateUnit = void 0;
	var _factory = requireFactory$b();
	const name = 'createUnit';
	const dependencies = ['typed', 'Unit'];
	createUnit$1.createCreateUnit = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Unit
	  } = _ref;
	  /**
	   * Create a user-defined unit and register it with the Unit type.
	   *
	   * Syntax:
	   *
	   *     math.createUnit({
	   *       baseUnit1: {
	   *         aliases: [string, ...]
	   *         prefixes: object
	   *       },
	   *       unit2: {
	   *         definition: string,
	   *         aliases: [string, ...]
	   *         prefixes: object,
	   *         offset: number
	   *       },
	   *       unit3: string    // Shortcut
	   *     })
	   *
	   *     // Another shortcut:
	   *     math.createUnit(string, unit : string, [object])
	   *
	   * Examples:
	   *
	   *     math.createUnit('foo')
	   *     math.createUnit('knot', {definition: '0.514444444 m/s', aliases: ['knots', 'kt', 'kts']})
	   *     math.createUnit('mph', '1 mile/hour')
	   *     math.createUnit('km', math.unit(1000, 'm'))
	   *
	   * @param {string} name      The name of the new unit. Must be unique. Example: 'knot'
	   * @param {string, UnitDefinition, Unit} definition      Definition of the unit in terms of existing units. For example, '0.514444444 m / s'.
	   * @param {Object} options   (optional) An object containing any of the following properties:
	   *     - `prefixes {string}` "none", "short", "long", "binary_short", or "binary_long". The default is "none".
	   *     - `aliases {Array}` Array of strings. Example: ['knots', 'kt', 'kts']
	   *     - `offset {Numeric}` An offset to apply when converting from the unit. For example, the offset for celsius is 273.15. Default is 0.
	   *
	   * See also:
	   *
	   *     unit
	   *
	   * @return {Unit} The new unit
	   */
	  return typed(name, {
	    // General function signature. First parameter is an object where each property is the definition of a new unit. The object keys are the unit names and the values are the definitions. The values can be objects, strings, or Units. If a property is an empty object or an empty string, a new base unit is created. The second parameter is the options.
	    'Object, Object': function (obj, options) {
	      return Unit.createUnit(obj, options);
	    },
	    // Same as above but without the options.
	    Object: function (obj) {
	      return Unit.createUnit(obj, {});
	    },
	    // Shortcut method for creating one unit.
	    'string, Unit | string | Object, Object': function (name, def, options) {
	      const obj = {};
	      obj[name] = def;
	      return Unit.createUnit(obj, options);
	    },
	    // Same as above but without the options.
	    'string, Unit | string | Object': function (name, def) {
	      const obj = {};
	      obj[name] = def;
	      return Unit.createUnit(obj, {});
	    },
	    // Without a definition, creates a base unit.
	    string: function (name) {
	      const obj = {};
	      obj[name] = {};
	      return Unit.createUnit(obj, {});
	    }
	  });
	});
	return createUnit$1;
}

var acos$1 = {};

var hasRequiredAcos$1;

function requireAcos$1 () {
	if (hasRequiredAcos$1) return acos$1;
	hasRequiredAcos$1 = 1;

	Object.defineProperty(acos$1, "__esModule", {
	  value: true
	});
	acos$1.createAcos = void 0;
	var _factory = requireFactory$b();
	const name = 'acos';
	const dependencies = ['typed', 'config', 'Complex'];
	acos$1.createAcos = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the inverse cosine of a value.
	   *
	   * To avoid confusion with the matrix arccosine, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.acos(x)
	   *
	   * Examples:
	   *
	   *    math.acos(0.5)           // returns number 1.0471975511965979
	   *    math.acos(math.cos(1.5)) // returns number 1.5
	   *
	   *    math.acos(2)             // returns Complex 0 + 1.3169578969248166 i
	   *
	   * See also:
	   *
	   *    cos, atan, asin
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} The arc cosine of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x >= -1 && x <= 1 || config.predictable) {
	        return Math.acos(x);
	      } else {
	        return new Complex(x, 0).acos();
	      }
	    },
	    Complex: function (x) {
	      return x.acos();
	    },
	    BigNumber: function (x) {
	      return x.acos();
	    }
	  });
	});
	return acos$1;
}

var acosh$1 = {};

var hasRequiredAcosh$1;

function requireAcosh$1 () {
	if (hasRequiredAcosh$1) return acosh$1;
	hasRequiredAcosh$1 = 1;

	Object.defineProperty(acosh$1, "__esModule", {
	  value: true
	});
	acosh$1.createAcosh = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'acosh';
	const dependencies = ['typed', 'config', 'Complex'];
	acosh$1.createAcosh = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the hyperbolic arccos of a value,
	   * defined as `acosh(x) = ln(sqrt(x^2 - 1) + x)`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.acosh(x)
	   *
	   * Examples:
	   *
	   *    math.acosh(1.5)       // returns 0.9624236501192069
	   *
	   * See also:
	   *
	   *    cosh, asinh, atanh
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic arccosine of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x >= 1 || config.predictable) {
	        return (0, _index.acoshNumber)(x);
	      }
	      if (x <= -1) {
	        return new Complex(Math.log(Math.sqrt(x * x - 1) - x), Math.PI);
	      }
	      return new Complex(x, 0).acosh();
	    },
	    Complex: function (x) {
	      return x.acosh();
	    },
	    BigNumber: function (x) {
	      return x.acosh();
	    }
	  });
	});
	return acosh$1;
}

var acot$1 = {};

var hasRequiredAcot$1;

function requireAcot$1 () {
	if (hasRequiredAcot$1) return acot$1;
	hasRequiredAcot$1 = 1;

	Object.defineProperty(acot$1, "__esModule", {
	  value: true
	});
	acot$1.createAcot = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'acot';
	const dependencies = ['typed', 'BigNumber'];
	acot$1.createAcot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the inverse cotangent of a value, defined as `acot(x) = atan(1/x)`.
	   *
	   * To avoid confusion with the matrix arccotanget, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.acot(x)
	   *
	   * Examples:
	   *
	   *    math.acot(0.5)           // returns number 1.1071487177940904
	   *    math.acot(2)             // returns number 0.4636476090008061
	   *    math.acot(math.cot(1.5)) // returns number 1.5
	   *
	   * See also:
	   *
	   *    cot, atan
	   *
	   * @param {number | BigNumber| Complex} x   Function input
	   * @return {number | BigNumber| Complex} The arc cotangent of x
	   */
	  return typed(name, {
	    number: _index.acotNumber,
	    Complex: function (x) {
	      return x.acot();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(1).div(x).atan();
	    }
	  });
	});
	return acot$1;
}

var acoth$1 = {};

var hasRequiredAcoth$1;

function requireAcoth$1 () {
	if (hasRequiredAcoth$1) return acoth$1;
	hasRequiredAcoth$1 = 1;

	Object.defineProperty(acoth$1, "__esModule", {
	  value: true
	});
	acoth$1.createAcoth = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'acoth';
	const dependencies = ['typed', 'config', 'Complex', 'BigNumber'];
	acoth$1.createAcoth = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the inverse hyperbolic tangent of a value,
	   * defined as `acoth(x) = atanh(1/x) = (ln((x+1)/x) + ln(x/(x-1))) / 2`.
	   *
	   * To avoid confusion with the matrix inverse hyperbolic tangent, this
	   * function does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.acoth(x)
	   *
	   * Examples:
	   *
	   *    math.acoth(0.5)     // returns 0.5493061443340548 - 1.5707963267948966i
	   *
	   * See also:
	   *
	   *    acsch, asech
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic arccotangent of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x >= 1 || x <= -1 || config.predictable) {
	        return (0, _index.acothNumber)(x);
	      }
	      return new Complex(x, 0).acoth();
	    },
	    Complex: function (x) {
	      return x.acoth();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(1).div(x).atanh();
	    }
	  });
	});
	return acoth$1;
}

var acsc$1 = {};

var hasRequiredAcsc$1;

function requireAcsc$1 () {
	if (hasRequiredAcsc$1) return acsc$1;
	hasRequiredAcsc$1 = 1;

	Object.defineProperty(acsc$1, "__esModule", {
	  value: true
	});
	acsc$1.createAcsc = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'acsc';
	const dependencies = ['typed', 'config', 'Complex', 'BigNumber'];
	acsc$1.createAcsc = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the inverse cosecant of a value, defined as `acsc(x) = asin(1/x)`.
	   *
	   * To avoid confusion with the matrix arccosecant, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.acsc(x)
	   *
	   * Examples:
	   *
	   *    math.acsc(2)             // returns 0.5235987755982989
	   *    math.acsc(0.5)           // returns Complex 1.5707963267948966 -1.3169578969248166i
	   *    math.acsc(math.csc(1.5)) // returns number 1.5
	   *
	   * See also:
	   *
	   *    csc, asin, asec
	   *
	   * @param {number | BigNumber | Complex} x   Function input
	   * @return {number | BigNumber | Complex} The arc cosecant of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x <= -1 || x >= 1 || config.predictable) {
	        return (0, _index.acscNumber)(x);
	      }
	      return new Complex(x, 0).acsc();
	    },
	    Complex: function (x) {
	      return x.acsc();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(1).div(x).asin();
	    }
	  });
	});
	return acsc$1;
}

var acsch$1 = {};

var hasRequiredAcsch$1;

function requireAcsch$1 () {
	if (hasRequiredAcsch$1) return acsch$1;
	hasRequiredAcsch$1 = 1;

	Object.defineProperty(acsch$1, "__esModule", {
	  value: true
	});
	acsch$1.createAcsch = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'acsch';
	const dependencies = ['typed', 'BigNumber'];
	acsch$1.createAcsch = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the inverse hyperbolic cosecant of a value,
	   * defined as `acsch(x) = asinh(1/x) = ln(1/x + sqrt(1/x^2 + 1))`.
	   *
	   * To avoid confusion with the matrix inverse hyperbolic cosecant, this function
	   * does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.acsch(x)
	   *
	   * Examples:
	   *
	   *    math.acsch(0.5)       // returns 1.4436354751788103
	   *
	   * See also:
	   *
	   *    asech, acoth
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic arccosecant of x
	   */
	  return typed(name, {
	    number: _index.acschNumber,
	    Complex: function (x) {
	      return x.acsch();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(1).div(x).asinh();
	    }
	  });
	});
	return acsch$1;
}

var asec$1 = {};

var hasRequiredAsec$1;

function requireAsec$1 () {
	if (hasRequiredAsec$1) return asec$1;
	hasRequiredAsec$1 = 1;

	Object.defineProperty(asec$1, "__esModule", {
	  value: true
	});
	asec$1.createAsec = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'asec';
	const dependencies = ['typed', 'config', 'Complex', 'BigNumber'];
	asec$1.createAsec = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the inverse secant of a value. Defined as `asec(x) = acos(1/x)`.
	   *
	   * To avoid confusion with the matrix arcsecant, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.asec(x)
	   *
	   * Examples:
	   *
	   *    math.asec(2)             // returns 1.0471975511965979
	   *    math.asec(math.sec(1.5)) // returns 1.5
	   *
	   *    math.asec(0.5)           // returns Complex 0 + 1.3169578969248166i
	   *
	   * See also:
	   *
	   *    acos, acot, acsc
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} The arc secant of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x <= -1 || x >= 1 || config.predictable) {
	        return (0, _index.asecNumber)(x);
	      }
	      return new Complex(x, 0).asec();
	    },
	    Complex: function (x) {
	      return x.asec();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(1).div(x).acos();
	    }
	  });
	});
	return asec$1;
}

var asech$1 = {};

var hasRequiredAsech$1;

function requireAsech$1 () {
	if (hasRequiredAsech$1) return asech$1;
	hasRequiredAsech$1 = 1;

	Object.defineProperty(asech$1, "__esModule", {
	  value: true
	});
	asech$1.createAsech = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'asech';
	const dependencies = ['typed', 'config', 'Complex', 'BigNumber'];
	asech$1.createAsech = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the hyperbolic arcsecant of a value,
	   * defined as `asech(x) = acosh(1/x) = ln(sqrt(1/x^2 - 1) + 1/x)`.
	   *
	   * To avoid confusion with the matrix hyperbolic arcsecant, this function
	   * does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.asech(x)
	   *
	   * Examples:
	   *
	   *    math.asech(0.5)       // returns 1.3169578969248166
	   *
	   * See also:
	   *
	   *    acsch, acoth
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic arcsecant of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x <= 1 && x >= -1 || config.predictable) {
	        const xInv = 1 / x;
	        if (xInv > 0 || config.predictable) {
	          return (0, _index.asechNumber)(x);
	        }
	        const ret = Math.sqrt(xInv * xInv - 1);
	        return new Complex(Math.log(ret - xInv), Math.PI);
	      }
	      return new Complex(x, 0).asech();
	    },
	    Complex: function (x) {
	      return x.asech();
	    },
	    BigNumber: function (x) {
	      return new BigNumber(1).div(x).acosh();
	    }
	  });
	});
	return asech$1;
}

var asin$1 = {};

var hasRequiredAsin$1;

function requireAsin$1 () {
	if (hasRequiredAsin$1) return asin$1;
	hasRequiredAsin$1 = 1;

	Object.defineProperty(asin$1, "__esModule", {
	  value: true
	});
	asin$1.createAsin = void 0;
	var _factory = requireFactory$b();
	const name = 'asin';
	const dependencies = ['typed', 'config', 'Complex'];
	asin$1.createAsin = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the inverse sine of a value.
	   *
	   * To avoid confusion with the matric arcsine, this function does not apply
	   * to matrices.
	   *
	   * Syntax:
	   *
	   *    math.asin(x)
	   *
	   * Examples:
	   *
	   *    math.asin(0.5)           // returns number 0.5235987755982989
	   *    math.asin(math.sin(1.5)) // returns number 1.5
	   *
	   *    math.asin(2)             // returns Complex 1.5707963267948966 -1.3169578969248166i
	   *
	   * See also:
	   *
	   *    sin, atan, acos
	   *
	   * @param {number | BigNumber | Complex} x   Function input
	   * @return {number | BigNumber | Complex} The arc sine of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x >= -1 && x <= 1 || config.predictable) {
	        return Math.asin(x);
	      } else {
	        return new Complex(x, 0).asin();
	      }
	    },
	    Complex: function (x) {
	      return x.asin();
	    },
	    BigNumber: function (x) {
	      return x.asin();
	    }
	  });
	});
	return asin$1;
}

var asinh$1 = {};

var hasRequiredAsinh$1;

function requireAsinh$1 () {
	if (hasRequiredAsinh$1) return asinh$1;
	hasRequiredAsinh$1 = 1;

	Object.defineProperty(asinh$1, "__esModule", {
	  value: true
	});
	asinh$1.createAsinh = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'asinh';
	const dependencies = ['typed'];
	asinh$1.createAsinh = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the hyperbolic arcsine of a value,
	   * defined as `asinh(x) = ln(x + sqrt(x^2 + 1))`.
	   *
	   * To avoid confusion with the matrix hyperbolic arcsine, this function
	   * does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.asinh(x)
	   *
	   * Examples:
	   *
	   *    math.asinh(0.5)       // returns 0.48121182505960347
	   *
	   * See also:
	   *
	   *    acosh, atanh
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic arcsine of x
	   */
	  return typed('asinh', {
	    number: _index.asinhNumber,
	    Complex: function (x) {
	      return x.asinh();
	    },
	    BigNumber: function (x) {
	      return x.asinh();
	    }
	  });
	});
	return asinh$1;
}

var atan$1 = {};

var hasRequiredAtan$1;

function requireAtan$1 () {
	if (hasRequiredAtan$1) return atan$1;
	hasRequiredAtan$1 = 1;

	Object.defineProperty(atan$1, "__esModule", {
	  value: true
	});
	atan$1.createAtan = void 0;
	var _factory = requireFactory$b();
	const name = 'atan';
	const dependencies = ['typed'];
	atan$1.createAtan = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the inverse tangent of a value.
	   *
	   * To avoid confusion with matrix arctangent, this function does not apply
	   * to matrices.
	   *
	   * Syntax:
	   *
	   *    math.atan(x)
	   *
	   * Examples:
	   *
	   *    math.atan(0.5)           // returns number 0.4636476090008061
	   *    math.atan(2)             // returns number 1.1071487177940904
	   *    math.atan(math.tan(1.5)) // returns number 1.5
	   *
	   * See also:
	   *
	   *    tan, asin, acos
	   *
	   * @param {number | BigNumber | Complex} x   Function input
	   * @return {number | BigNumber | Complex} The arc tangent of x
	   */
	  return typed('atan', {
	    number: function (x) {
	      return Math.atan(x);
	    },
	    Complex: function (x) {
	      return x.atan();
	    },
	    BigNumber: function (x) {
	      return x.atan();
	    }
	  });
	});
	return atan$1;
}

var atan2$1 = {};

var hasRequiredAtan2$1;

function requireAtan2$1 () {
	if (hasRequiredAtan2$1) return atan2$1;
	hasRequiredAtan2$1 = 1;

	Object.defineProperty(atan2$1, "__esModule", {
	  value: true
	});
	atan2$1.createAtan2 = void 0;
	var _factory = requireFactory$b();
	var _matAlgo02xDS = requireMatAlgo02xDS0();
	var _matAlgo03xDSf = requireMatAlgo03xDSf();
	var _matAlgo09xS0Sf = requireMatAlgo09xS0Sf();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo12xSfs = requireMatAlgo12xSfs();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'atan2';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'BigNumber', 'DenseMatrix', 'concat'];
	atan2$1.createAtan2 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    BigNumber,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const matAlgo02xDS0 = (0, _matAlgo02xDS.createMatAlgo02xDS0)({
	    typed,
	    equalScalar
	  });
	  const matAlgo03xDSf = (0, _matAlgo03xDSf.createMatAlgo03xDSf)({
	    typed
	  });
	  const matAlgo09xS0Sf = (0, _matAlgo09xS0Sf.createMatAlgo09xS0Sf)({
	    typed,
	    equalScalar
	  });
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo12xSfs = (0, _matAlgo12xSfs.createMatAlgo12xSfs)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });

	  /**
	   * Calculate the inverse tangent function with two arguments, y/x.
	   * By providing two arguments, the right quadrant of the computed angle can be
	   * determined.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.atan2(y, x)
	   *
	   * Examples:
	   *
	   *    math.atan2(2, 2) / math.pi       // returns number 0.25
	   *
	   *    const angle = math.unit(60, 'deg')
	   *    const x = math.cos(angle)
	   *    const y = math.sin(angle)
	   *    math.atan2(y, x) * 180 / math.pi  // returns 60
	   *
	   *    math.atan(2)             // returns number 1.1071487177940904
	   *
	   * See also:
	   *
	   *    tan, atan, sin, cos
	   *
	   * @param {number | Array | Matrix} y  Second dimension
	   * @param {number | Array | Matrix} x  First dimension
	   * @return {number | Array | Matrix} Four-quadrant inverse tangent
	   */
	  return typed(name, {
	    'number, number': Math.atan2,
	    // Complex numbers doesn't seem to have a reasonable implementation of
	    // atan2(). Even Matlab removed the support, after they only calculated
	    // the atan only on base of the real part of the numbers and ignored
	    // the imaginary.

	    'BigNumber, BigNumber': (y, x) => BigNumber.atan2(y, x)
	  }, matrixAlgorithmSuite({
	    scalar: 'number | BigNumber',
	    SS: matAlgo09xS0Sf,
	    DS: matAlgo03xDSf,
	    SD: matAlgo02xDS0,
	    Ss: matAlgo11xS0s,
	    sS: matAlgo12xSfs
	  }));
	});
	return atan2$1;
}

var atanh$1 = {};

var hasRequiredAtanh$1;

function requireAtanh$1 () {
	if (hasRequiredAtanh$1) return atanh$1;
	hasRequiredAtanh$1 = 1;

	Object.defineProperty(atanh$1, "__esModule", {
	  value: true
	});
	atanh$1.createAtanh = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'atanh';
	const dependencies = ['typed', 'config', 'Complex'];
	atanh$1.createAtanh = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the hyperbolic arctangent of a value,
	   * defined as `atanh(x) = ln((1 + x)/(1 - x)) / 2`.
	   *
	   * To avoid confusion with the matrix hyperbolic arctangent, this function
	   * does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.atanh(x)
	   *
	   * Examples:
	   *
	   *    math.atanh(0.5)       // returns 0.5493061443340549
	   *
	   * See also:
	   *
	   *    acosh, asinh
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic arctangent of x
	   */
	  return typed(name, {
	    number: function (x) {
	      if (x <= 1 && x >= -1 || config.predictable) {
	        return (0, _index.atanhNumber)(x);
	      }
	      return new Complex(x, 0).atanh();
	    },
	    Complex: function (x) {
	      return x.atanh();
	    },
	    BigNumber: function (x) {
	      return x.atanh();
	    }
	  });
	});
	return atanh$1;
}

var cos$1 = {};

var trigUnit = {};

var hasRequiredTrigUnit;

function requireTrigUnit () {
	if (hasRequiredTrigUnit) return trigUnit;
	hasRequiredTrigUnit = 1;

	Object.defineProperty(trigUnit, "__esModule", {
	  value: true
	});
	trigUnit.createTrigUnit = void 0;
	var _factory = requireFactory$b();
	trigUnit.createTrigUnit = /* #__PURE__ */(0, _factory.factory)('trigUnit', ['typed'], _ref => {
	  let {
	    typed
	  } = _ref;
	  return {
	    Unit: typed.referToSelf(self => x => {
	      if (!x.hasBase(x.constructor.BASE_UNITS.ANGLE)) {
	        throw new TypeError('Unit in function cot is no angle');
	      }
	      return typed.find(self, x.valueType())(x.value);
	    })
	  };
	});
	return trigUnit;
}

var hasRequiredCos$1;

function requireCos$1 () {
	if (hasRequiredCos$1) return cos$1;
	hasRequiredCos$1 = 1;

	Object.defineProperty(cos$1, "__esModule", {
	  value: true
	});
	cos$1.createCos = void 0;
	var _factory = requireFactory$b();
	var _trigUnit = requireTrigUnit();
	const name = 'cos';
	const dependencies = ['typed'];
	cos$1.createCos = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  const trigUnit = (0, _trigUnit.createTrigUnit)({
	    typed
	  });

	  /**
	   * Calculate the cosine of a value.
	   *
	   * To avoid confusion with the matrix cosine, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.cos(x)
	   *
	   * Examples:
	   *
	   *    math.cos(2)                      // returns number -0.4161468365471422
	   *    math.cos(math.pi / 4)            // returns number  0.7071067811865475
	   *    math.cos(math.unit(180, 'deg'))  // returns number -1
	   *    math.cos(math.unit(60, 'deg'))   // returns number  0.5
	   *
	   *    const angle = 0.2
	   *    math.pow(math.sin(angle), 2) + math.pow(math.cos(angle), 2) // returns number 1
	   *
	   * See also:
	   *
	   *    cos, tan
	   *
	   * @param {number | BigNumber | Complex | Unit} x  Function input
	   * @return {number | BigNumber | Complex} Cosine of x
	   */
	  return typed(name, {
	    number: Math.cos,
	    'Complex | BigNumber': x => x.cos()
	  }, trigUnit);
	});
	return cos$1;
}

var cosh$1 = {};

var hasRequiredCosh$1;

function requireCosh$1 () {
	if (hasRequiredCosh$1) return cosh$1;
	hasRequiredCosh$1 = 1;

	Object.defineProperty(cosh$1, "__esModule", {
	  value: true
	});
	cosh$1.createCosh = void 0;
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	const name = 'cosh';
	const dependencies = ['typed'];
	cosh$1.createCosh = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the hyperbolic cosine of a value,
	   * defined as `cosh(x) = 1/2 * (exp(x) + exp(-x))`.
	   *
	   * To avoid confusion with the matrix hyperbolic cosine, this function does
	   * not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.cosh(x)
	   *
	   * Examples:
	   *
	   *    math.cosh(0.5)       // returns number 1.1276259652063807
	   *
	   * See also:
	   *
	   *    sinh, tanh
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic cosine of x
	   */
	  return typed(name, {
	    number: _number.cosh,
	    'Complex | BigNumber': x => x.cosh()
	  });
	});
	return cosh$1;
}

var cot$1 = {};

var hasRequiredCot$1;

function requireCot$1 () {
	if (hasRequiredCot$1) return cot$1;
	hasRequiredCot$1 = 1;

	Object.defineProperty(cot$1, "__esModule", {
	  value: true
	});
	cot$1.createCot = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	var _trigUnit = requireTrigUnit();
	const name = 'cot';
	const dependencies = ['typed', 'BigNumber'];
	cot$1.createCot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  const trigUnit = (0, _trigUnit.createTrigUnit)({
	    typed
	  });

	  /**
	   * Calculate the cotangent of a value. Defined as `cot(x) = 1 / tan(x)`.
	   *
	   * To avoid confusion with the matrix cotangent, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.cot(x)
	   *
	   * Examples:
	   *
	   *    math.cot(2)      // returns number -0.45765755436028577
	   *    1 / math.tan(2)  // returns number -0.45765755436028577
	   *
	   * See also:
	   *
	   *    tan, sec, csc
	   *
	   * @param {number | Complex | Unit | Array | Matrix} x  Function input
	   * @return {number | Complex | Array | Matrix} Cotangent of x
	   */
	  return typed(name, {
	    number: _index.cotNumber,
	    Complex: x => x.cot(),
	    BigNumber: x => new BigNumber(1).div(x.tan())
	  }, trigUnit);
	});
	return cot$1;
}

var coth$1 = {};

var hasRequiredCoth$1;

function requireCoth$1 () {
	if (hasRequiredCoth$1) return coth$1;
	hasRequiredCoth$1 = 1;

	Object.defineProperty(coth$1, "__esModule", {
	  value: true
	});
	coth$1.createCoth = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'coth';
	const dependencies = ['typed', 'BigNumber'];
	coth$1.createCoth = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the hyperbolic cotangent of a value,
	   * defined as `coth(x) = 1 / tanh(x)`.
	   *
	   * To avoid confusion with the matrix hyperbolic cotangent, this function
	   * does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.coth(x)
	   *
	   * Examples:
	   *
	   *    // coth(x) = 1 / tanh(x)
	   *    math.coth(2)         // returns 1.0373147207275482
	   *    1 / math.tanh(2)     // returns 1.0373147207275482
	   *
	   * See also:
	   *
	   *    sinh, tanh, cosh
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic cotangent of x
	   */
	  return typed(name, {
	    number: _index.cothNumber,
	    Complex: x => x.coth(),
	    BigNumber: x => new BigNumber(1).div(x.tanh())
	  });
	});
	return coth$1;
}

var csc$1 = {};

var hasRequiredCsc$1;

function requireCsc$1 () {
	if (hasRequiredCsc$1) return csc$1;
	hasRequiredCsc$1 = 1;

	Object.defineProperty(csc$1, "__esModule", {
	  value: true
	});
	csc$1.createCsc = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	var _trigUnit = requireTrigUnit();
	const name = 'csc';
	const dependencies = ['typed', 'BigNumber'];
	csc$1.createCsc = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  const trigUnit = (0, _trigUnit.createTrigUnit)({
	    typed
	  });

	  /**
	   * Calculate the cosecant of a value, defined as `csc(x) = 1/sin(x)`.
	   *
	   * To avoid confusion with the matrix cosecant, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.csc(x)
	   *
	   * Examples:
	   *
	   *    math.csc(2)      // returns number 1.099750170294617
	   *    1 / math.sin(2)  // returns number 1.099750170294617
	   *
	   * See also:
	   *
	   *    sin, sec, cot
	   *
	   * @param {number | BigNumber | Complex | Unit} x  Function input
	   * @return {number | BigNumber | Complex} Cosecant of x
	   */
	  return typed(name, {
	    number: _index.cscNumber,
	    Complex: x => x.csc(),
	    BigNumber: x => new BigNumber(1).div(x.sin())
	  }, trigUnit);
	});
	return csc$1;
}

var csch$1 = {};

var hasRequiredCsch$1;

function requireCsch$1 () {
	if (hasRequiredCsch$1) return csch$1;
	hasRequiredCsch$1 = 1;

	Object.defineProperty(csch$1, "__esModule", {
	  value: true
	});
	csch$1.createCsch = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'csch';
	const dependencies = ['typed', 'BigNumber'];
	csch$1.createCsch = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the hyperbolic cosecant of a value,
	   * defined as `csch(x) = 1 / sinh(x)`.
	   *
	   * To avoid confusion with the matrix hyperbolic cosecant, this function
	   * does not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.csch(x)
	   *
	   * Examples:
	   *
	   *    // csch(x) = 1/ sinh(x)
	   *    math.csch(0.5)       // returns 1.9190347513349437
	   *    1 / math.sinh(0.5)   // returns 1.9190347513349437
	   *
	   * See also:
	   *
	   *    sinh, sech, coth
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic cosecant of x
	   */
	  return typed(name, {
	    number: _index.cschNumber,
	    Complex: x => x.csch(),
	    BigNumber: x => new BigNumber(1).div(x.sinh())
	  });
	});
	return csch$1;
}

var sec$1 = {};

var hasRequiredSec$1;

function requireSec$1 () {
	if (hasRequiredSec$1) return sec$1;
	hasRequiredSec$1 = 1;

	Object.defineProperty(sec$1, "__esModule", {
	  value: true
	});
	sec$1.createSec = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	var _trigUnit = requireTrigUnit();
	const name = 'sec';
	const dependencies = ['typed', 'BigNumber'];
	sec$1.createSec = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  const trigUnit = (0, _trigUnit.createTrigUnit)({
	    typed
	  });

	  /**
	   * Calculate the secant of a value, defined as `sec(x) = 1/cos(x)`.
	   *
	   * To avoid confusion with the matrix secant, this function does not
	   * apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.sec(x)
	   *
	   * Examples:
	   *
	   *    math.sec(2)      // returns number -2.4029979617223822
	   *    1 / math.cos(2)  // returns number -2.4029979617223822
	   *
	   * See also:
	   *
	   *    cos, csc, cot
	   *
	   * @param {number | BigNumber | Complex | Unit} x  Function input
	   * @return {number | BigNumber | Complex} Secant of x
	   */
	  return typed(name, {
	    number: _index.secNumber,
	    Complex: x => x.sec(),
	    BigNumber: x => new BigNumber(1).div(x.cos())
	  }, trigUnit);
	});
	return sec$1;
}

var sech$1 = {};

var hasRequiredSech$1;

function requireSech$1 () {
	if (hasRequiredSech$1) return sech$1;
	hasRequiredSech$1 = 1;

	Object.defineProperty(sech$1, "__esModule", {
	  value: true
	});
	sech$1.createSech = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'sech';
	const dependencies = ['typed', 'BigNumber'];
	sech$1.createSech = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    BigNumber
	  } = _ref;
	  /**
	   * Calculate the hyperbolic secant of a value,
	   * defined as `sech(x) = 1 / cosh(x)`.
	   *
	   * To avoid confusion with the matrix hyperbolic secant, this function does
	   * not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.sech(x)
	   *
	   * Examples:
	   *
	   *    // sech(x) = 1/ cosh(x)
	   *    math.sech(0.5)       // returns 0.886818883970074
	   *    1 / math.cosh(0.5)   // returns 0.886818883970074
	   *
	   * See also:
	   *
	   *    cosh, csch, coth
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic secant of x
	   */
	  return typed(name, {
	    number: _index.sechNumber,
	    Complex: x => x.sech(),
	    BigNumber: x => new BigNumber(1).div(x.cosh())
	  });
	});
	return sech$1;
}

var sin$1 = {};

var hasRequiredSin$1;

function requireSin$1 () {
	if (hasRequiredSin$1) return sin$1;
	hasRequiredSin$1 = 1;

	Object.defineProperty(sin$1, "__esModule", {
	  value: true
	});
	sin$1.createSin = void 0;
	var _factory = requireFactory$b();
	var _trigUnit = requireTrigUnit();
	const name = 'sin';
	const dependencies = ['typed'];
	sin$1.createSin = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  const trigUnit = (0, _trigUnit.createTrigUnit)({
	    typed
	  });

	  /**
	   * Calculate the sine of a value.
	   *
	   * To avoid confusion with the matrix sine, this function does not apply
	   * to matrices.
	   *
	   * Syntax:
	   *
	   *    math.sin(x)
	   *
	   * Examples:
	   *
	   *    math.sin(2)                      // returns number 0.9092974268256813
	   *    math.sin(math.pi / 4)            // returns number 0.7071067811865475
	   *    math.sin(math.unit(90, 'deg'))   // returns number 1
	   *    math.sin(math.unit(30, 'deg'))   // returns number 0.5
	   *
	   *    const angle = 0.2
	   *    math.pow(math.sin(angle), 2) + math.pow(math.cos(angle), 2) // returns number 1
	   *
	   * See also:
	   *
	   *    cos, tan
	   *
	   * @param {number | BigNumber | Complex | Unit} x  Function input
	   * @return {number | BigNumber | Complex} Sine of x
	   */
	  return typed(name, {
	    number: Math.sin,
	    'Complex | BigNumber': x => x.sin()
	  }, trigUnit);
	});
	return sin$1;
}

var sinh$1 = {};

var hasRequiredSinh$1;

function requireSinh$1 () {
	if (hasRequiredSinh$1) return sinh$1;
	hasRequiredSinh$1 = 1;

	Object.defineProperty(sinh$1, "__esModule", {
	  value: true
	});
	sinh$1.createSinh = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'sinh';
	const dependencies = ['typed'];
	sinh$1.createSinh = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the hyperbolic sine of a value,
	   * defined as `sinh(x) = 1/2 * (exp(x) - exp(-x))`.
	   *
	   * To avoid confusion with the matrix hyperbolic sine, this function does
	   * not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.sinh(x)
	   *
	   * Examples:
	   *
	   *    math.sinh(0.5)       // returns number 0.5210953054937474
	   *
	   * See also:
	   *
	   *    cosh, tanh
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic sine of x
	   */
	  return typed(name, {
	    number: _index.sinhNumber,
	    'Complex | BigNumber': x => x.sinh()
	  });
	});
	return sinh$1;
}

var tan$1 = {};

var hasRequiredTan$1;

function requireTan$1 () {
	if (hasRequiredTan$1) return tan$1;
	hasRequiredTan$1 = 1;

	Object.defineProperty(tan$1, "__esModule", {
	  value: true
	});
	tan$1.createTan = void 0;
	var _factory = requireFactory$b();
	var _trigUnit = requireTrigUnit();
	const name = 'tan';
	const dependencies = ['typed'];
	tan$1.createTan = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  const trigUnit = (0, _trigUnit.createTrigUnit)({
	    typed
	  });

	  /**
	   * Calculate the tangent of a value. `tan(x)` is equal to `sin(x) / cos(x)`.
	   *
	   * To avoid confusion with the matrix tangent, this function does not apply
	   * to matrices.
	   *
	   * Syntax:
	   *
	   *    math.tan(x)
	   *
	   * Examples:
	   *
	   *    math.tan(0.5)                    // returns number 0.5463024898437905
	   *    math.sin(0.5) / math.cos(0.5)    // returns number 0.5463024898437905
	   *    math.tan(math.pi / 4)            // returns number 1
	   *    math.tan(math.unit(45, 'deg'))   // returns number 1
	   *
	   * See also:
	   *
	   *    atan, sin, cos
	   *
	   * @param {number | BigNumber | Complex | Unit} x  Function input
	   * @return {number | BigNumber | Complex} Tangent of x
	   */
	  return typed(name, {
	    number: Math.tan,
	    'Complex | BigNumber': x => x.tan()
	  }, trigUnit);
	});
	return tan$1;
}

var tanh$1 = {};

var hasRequiredTanh$1;

function requireTanh$1 () {
	if (hasRequiredTanh$1) return tanh$1;
	hasRequiredTanh$1 = 1;

	Object.defineProperty(tanh$1, "__esModule", {
	  value: true
	});
	tanh$1.createTanh = void 0;
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	const name = 'tanh';
	const dependencies = ['typed'];
	tanh$1.createTanh = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Calculate the hyperbolic tangent of a value,
	   * defined as `tanh(x) = (exp(2 * x) - 1) / (exp(2 * x) + 1)`.
	   *
	   * To avoid confusion with matrix hyperbolic tangent, this function does
	   * not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.tanh(x)
	   *
	   * Examples:
	   *
	   *    // tanh(x) = sinh(x) / cosh(x) = 1 / coth(x)
	   *    math.tanh(0.5)                   // returns 0.46211715726000974
	   *    math.sinh(0.5) / math.cosh(0.5)  // returns 0.46211715726000974
	   *    1 / math.coth(0.5)               // returns 0.46211715726000974
	   *
	   * See also:
	   *
	   *    sinh, cosh, coth
	   *
	   * @param {number | BigNumber | Complex} x  Function input
	   * @return {number | BigNumber | Complex} Hyperbolic tangent of x
	   */
	  return typed('tanh', {
	    number: _number.tanh,
	    'Complex | BigNumber': x => x.tanh()
	  });
	});
	return tanh$1;
}

var setCartesian$1 = {};

var hasRequiredSetCartesian$1;

function requireSetCartesian$1 () {
	if (hasRequiredSetCartesian$1) return setCartesian$1;
	hasRequiredSetCartesian$1 = 1;

	Object.defineProperty(setCartesian$1, "__esModule", {
	  value: true
	});
	setCartesian$1.createSetCartesian = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setCartesian';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index', 'DenseMatrix'];
	setCartesian$1.createSetCartesian = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index,
	    DenseMatrix
	  } = _ref;
	  /**
	   * Create the cartesian product of two (multi)sets.
	   * Multi-dimension arrays will be converted to single-dimension arrays
	   * and the values will be sorted in ascending order before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setCartesian(set1, set2)
	   *
	   * Examples:
	   *
	   *    math.setCartesian([1, 2], [3, 4])        // returns [[1, 3], [1, 4], [2, 3], [2, 4]]
	   *    math.setCartesian([4, 3], [2, 1])        // returns [[3, 1], [3, 2], [4, 1], [4, 2]]
	   *
	   * See also:
	   *
	   *    setUnion, setIntersect, setDifference, setPowerset
	   *
	   * @param {Array | Matrix}    a1  A (multi)set
	   * @param {Array | Matrix}    a2  A (multi)set
	   * @return {Array | Matrix}    The cartesian product of two (multi)sets
	   */
	  return typed(name, {
	    'Array | Matrix, Array | Matrix': function (a1, a2) {
	      let result = [];
	      if (subset(size(a1), new Index(0)) !== 0 && subset(size(a2), new Index(0)) !== 0) {
	        // if any of them is empty, return empty
	        const b1 = (0, _array.flatten)(Array.isArray(a1) ? a1 : a1.toArray()).sort(compareNatural);
	        const b2 = (0, _array.flatten)(Array.isArray(a2) ? a2 : a2.toArray()).sort(compareNatural);
	        result = [];
	        for (let i = 0; i < b1.length; i++) {
	          for (let j = 0; j < b2.length; j++) {
	            result.push([b1[i], b2[j]]);
	          }
	        }
	      }
	      // return an array, if both inputs were arrays
	      if (Array.isArray(a1) && Array.isArray(a2)) {
	        return result;
	      }
	      // return a matrix otherwise
	      return new DenseMatrix(result);
	    }
	  });
	});
	return setCartesian$1;
}

var setDifference$1 = {};

var hasRequiredSetDifference$1;

function requireSetDifference$1 () {
	if (hasRequiredSetDifference$1) return setDifference$1;
	hasRequiredSetDifference$1 = 1;

	Object.defineProperty(setDifference$1, "__esModule", {
	  value: true
	});
	setDifference$1.createSetDifference = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setDifference';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index', 'DenseMatrix'];
	setDifference$1.createSetDifference = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index,
	    DenseMatrix
	  } = _ref;
	  /**
	   * Create the difference of two (multi)sets: every element of set1, that is not the element of set2.
	   * Multi-dimension arrays will be converted to single-dimension arrays before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setDifference(set1, set2)
	   *
	   * Examples:
	   *
	   *    math.setDifference([1, 2, 3, 4], [3, 4, 5, 6])            // returns [1, 2]
	   *    math.setDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]])    // returns [1, 2]
	   *
	   * See also:
	   *
	   *    setUnion, setIntersect, setSymDifference
	   *
	   * @param {Array | Matrix}    a1  A (multi)set
	   * @param {Array | Matrix}    a2  A (multi)set
	   * @return {Array | Matrix}    The difference of two (multi)sets
	   */
	  return typed(name, {
	    'Array | Matrix, Array | Matrix': function (a1, a2) {
	      let result;
	      if (subset(size(a1), new Index(0)) === 0) {
	        // empty-anything=empty
	        result = [];
	      } else if (subset(size(a2), new Index(0)) === 0) {
	        // anything-empty=anything
	        return (0, _array.flatten)(a1.toArray());
	      } else {
	        const b1 = (0, _array.identify)((0, _array.flatten)(Array.isArray(a1) ? a1 : a1.toArray()).sort(compareNatural));
	        const b2 = (0, _array.identify)((0, _array.flatten)(Array.isArray(a2) ? a2 : a2.toArray()).sort(compareNatural));
	        result = [];
	        let inb2;
	        for (let i = 0; i < b1.length; i++) {
	          inb2 = false;
	          for (let j = 0; j < b2.length; j++) {
	            if (compareNatural(b1[i].value, b2[j].value) === 0 && b1[i].identifier === b2[j].identifier) {
	              // the identifier is always a decimal int
	              inb2 = true;
	              break;
	            }
	          }
	          if (!inb2) {
	            result.push(b1[i]);
	          }
	        }
	      }
	      // return an array, if both inputs were arrays
	      if (Array.isArray(a1) && Array.isArray(a2)) {
	        return (0, _array.generalize)(result);
	      }
	      // return a matrix otherwise
	      return new DenseMatrix((0, _array.generalize)(result));
	    }
	  });
	});
	return setDifference$1;
}

var setDistinct$1 = {};

var hasRequiredSetDistinct$1;

function requireSetDistinct$1 () {
	if (hasRequiredSetDistinct$1) return setDistinct$1;
	hasRequiredSetDistinct$1 = 1;

	Object.defineProperty(setDistinct$1, "__esModule", {
	  value: true
	});
	setDistinct$1.createSetDistinct = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setDistinct';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index', 'DenseMatrix'];
	setDistinct$1.createSetDistinct = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index,
	    DenseMatrix
	  } = _ref;
	  /**
	   * Collect the distinct elements of a multiset.
	   * A multi-dimension array will be converted to a single-dimension array before the operation.
	   * The items of the returned array will be sorted in natural order.
	   *
	   * Syntax:
	   *
	   *    math.setDistinct(set)
	   *
	   * Examples:
	   *
	   *    math.setDistinct([1, 1, 1, 2, 2, 3])        // returns [1, 2, 3]
	   *
	   * See also:
	   *
	   *    setMultiplicity
	   *
	   * @param {Array | Matrix}    a  A multiset
	   * @return {Array | Matrix}    A set containing the distinct elements of the multiset
	   */
	  return typed(name, {
	    'Array | Matrix': function (a) {
	      let result;
	      if (subset(size(a), new Index(0)) === 0) {
	        // if empty, return empty
	        result = [];
	      } else {
	        const b = (0, _array.flatten)(Array.isArray(a) ? a : a.toArray()).sort(compareNatural);
	        result = [];
	        result.push(b[0]);
	        for (let i = 1; i < b.length; i++) {
	          if (compareNatural(b[i], b[i - 1]) !== 0) {
	            result.push(b[i]);
	          }
	        }
	      }
	      // return an array, if the input was an array
	      if (Array.isArray(a)) {
	        return result;
	      }
	      // return a matrix otherwise
	      return new DenseMatrix(result);
	    }
	  });
	});
	return setDistinct$1;
}

var setIntersect$1 = {};

var hasRequiredSetIntersect$1;

function requireSetIntersect$1 () {
	if (hasRequiredSetIntersect$1) return setIntersect$1;
	hasRequiredSetIntersect$1 = 1;

	Object.defineProperty(setIntersect$1, "__esModule", {
	  value: true
	});
	setIntersect$1.createSetIntersect = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setIntersect';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index', 'DenseMatrix'];
	setIntersect$1.createSetIntersect = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index,
	    DenseMatrix
	  } = _ref;
	  /**
	   * Create the intersection of two (multi)sets.
	   * Multi-dimension arrays will be converted to single-dimension arrays before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setIntersect(set1, set2)
	   *
	   * Examples:
	   *
	   *    math.setIntersect([1, 2, 3, 4], [3, 4, 5, 6])            // returns [3, 4]
	   *    math.setIntersect([[1, 2], [3, 4]], [[3, 4], [5, 6]])    // returns [3, 4]
	   *
	   * See also:
	   *
	   *    setUnion, setDifference
	   *
	   * @param {Array | Matrix}    a1  A (multi)set
	   * @param {Array | Matrix}    a2  A (multi)set
	   * @return {Array | Matrix}    The intersection of two (multi)sets
	   */
	  return typed(name, {
	    'Array | Matrix, Array | Matrix': function (a1, a2) {
	      let result;
	      if (subset(size(a1), new Index(0)) === 0 || subset(size(a2), new Index(0)) === 0) {
	        // of any of them is empty, return empty
	        result = [];
	      } else {
	        const b1 = (0, _array.identify)((0, _array.flatten)(Array.isArray(a1) ? a1 : a1.toArray()).sort(compareNatural));
	        const b2 = (0, _array.identify)((0, _array.flatten)(Array.isArray(a2) ? a2 : a2.toArray()).sort(compareNatural));
	        result = [];
	        for (let i = 0; i < b1.length; i++) {
	          for (let j = 0; j < b2.length; j++) {
	            if (compareNatural(b1[i].value, b2[j].value) === 0 && b1[i].identifier === b2[j].identifier) {
	              // the identifier is always a decimal int
	              result.push(b1[i]);
	              break;
	            }
	          }
	        }
	      }
	      // return an array, if both inputs were arrays
	      if (Array.isArray(a1) && Array.isArray(a2)) {
	        return (0, _array.generalize)(result);
	      }
	      // return a matrix otherwise
	      return new DenseMatrix((0, _array.generalize)(result));
	    }
	  });
	});
	return setIntersect$1;
}

var setIsSubset$1 = {};

var hasRequiredSetIsSubset$1;

function requireSetIsSubset$1 () {
	if (hasRequiredSetIsSubset$1) return setIsSubset$1;
	hasRequiredSetIsSubset$1 = 1;

	Object.defineProperty(setIsSubset$1, "__esModule", {
	  value: true
	});
	setIsSubset$1.createSetIsSubset = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setIsSubset';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index'];
	setIsSubset$1.createSetIsSubset = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index
	  } = _ref;
	  /**
	   * Check whether a (multi)set is a subset of another (multi)set. (Every element of set1 is the element of set2.)
	   * Multi-dimension arrays will be converted to single-dimension arrays before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setIsSubset(set1, set2)
	   *
	   * Examples:
	   *
	   *    math.setIsSubset([1, 2], [3, 4, 5, 6])        // returns false
	   *    math.setIsSubset([3, 4], [3, 4, 5, 6])        // returns true
	   *
	   * See also:
	   *
	   *    setUnion, setIntersect, setDifference
	   *
	   * @param {Array | Matrix}    a1  A (multi)set
	   * @param {Array | Matrix}    a2  A (multi)set
	   * @return {boolean} Returns true when a1 is a subset of a2, returns false otherwise
	   */
	  return typed(name, {
	    'Array | Matrix, Array | Matrix': function (a1, a2) {
	      if (subset(size(a1), new Index(0)) === 0) {
	        // empty is a subset of anything
	        return true;
	      } else if (subset(size(a2), new Index(0)) === 0) {
	        // anything is not a subset of empty
	        return false;
	      }
	      const b1 = (0, _array.identify)((0, _array.flatten)(Array.isArray(a1) ? a1 : a1.toArray()).sort(compareNatural));
	      const b2 = (0, _array.identify)((0, _array.flatten)(Array.isArray(a2) ? a2 : a2.toArray()).sort(compareNatural));
	      let inb2;
	      for (let i = 0; i < b1.length; i++) {
	        inb2 = false;
	        for (let j = 0; j < b2.length; j++) {
	          if (compareNatural(b1[i].value, b2[j].value) === 0 && b1[i].identifier === b2[j].identifier) {
	            // the identifier is always a decimal int
	            inb2 = true;
	            break;
	          }
	        }
	        if (inb2 === false) {
	          return false;
	        }
	      }
	      return true;
	    }
	  });
	});
	return setIsSubset$1;
}

var setMultiplicity$1 = {};

var hasRequiredSetMultiplicity$1;

function requireSetMultiplicity$1 () {
	if (hasRequiredSetMultiplicity$1) return setMultiplicity$1;
	hasRequiredSetMultiplicity$1 = 1;

	Object.defineProperty(setMultiplicity$1, "__esModule", {
	  value: true
	});
	setMultiplicity$1.createSetMultiplicity = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setMultiplicity';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index'];
	setMultiplicity$1.createSetMultiplicity = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index
	  } = _ref;
	  /**
	   * Count the multiplicity of an element in a multiset.
	   * A multi-dimension array will be converted to a single-dimension array before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setMultiplicity(element, set)
	   *
	   * Examples:
	   *
	   *    math.setMultiplicity(1, [1, 2, 2, 4])    // returns 1
	   *    math.setMultiplicity(2, [1, 2, 2, 4])    // returns 2
	   *
	   * See also:
	   *
	   *    setDistinct, setSize
	   *
	   * @param {number | BigNumber | Fraction | Complex} e  An element in the multiset
	   * @param {Array | Matrix}     a  A multiset
	   * @return {number}            The number of how many times the multiset contains the element
	   */
	  return typed(name, {
	    'number | BigNumber | Fraction | Complex, Array | Matrix': function (e, a) {
	      if (subset(size(a), new Index(0)) === 0) {
	        // if empty, return 0
	        return 0;
	      }
	      const b = (0, _array.flatten)(Array.isArray(a) ? a : a.toArray());
	      let count = 0;
	      for (let i = 0; i < b.length; i++) {
	        if (compareNatural(b[i], e) === 0) {
	          count++;
	        }
	      }
	      return count;
	    }
	  });
	});
	return setMultiplicity$1;
}

var setPowerset$1 = {};

var hasRequiredSetPowerset$1;

function requireSetPowerset$1 () {
	if (hasRequiredSetPowerset$1) return setPowerset$1;
	hasRequiredSetPowerset$1 = 1;

	Object.defineProperty(setPowerset$1, "__esModule", {
	  value: true
	});
	setPowerset$1.createSetPowerset = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setPowerset';
	const dependencies = ['typed', 'size', 'subset', 'compareNatural', 'Index'];
	setPowerset$1.createSetPowerset = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    subset,
	    compareNatural,
	    Index
	  } = _ref;
	  /**
	   * Create the powerset of a (multi)set. (The powerset contains very possible subsets of a (multi)set.)
	   * A multi-dimension array will be converted to a single-dimension array before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setPowerset(set)
	   *
	   * Examples:
	   *
	   *    math.setPowerset([1, 2, 3])        // returns [[], [1], [2], [3], [1, 2], [1, 3], [2, 3], [1, 2, 3]]
	   *
	   * See also:
	   *
	   *    setCartesian
	   *
	   * @param {Array | Matrix}    a  A (multi)set
	   * @return {Array}    The powerset of the (multi)set
	   */
	  return typed(name, {
	    'Array | Matrix': function (a) {
	      if (subset(size(a), new Index(0)) === 0) {
	        // if empty, return empty
	        return [];
	      }
	      const b = (0, _array.flatten)(Array.isArray(a) ? a : a.toArray()).sort(compareNatural);
	      const result = [];
	      let number = 0;
	      while (number.toString(2).length <= b.length) {
	        result.push(_subset(b, number.toString(2).split('').reverse()));
	        number++;
	      }
	      // can not return a matrix, because of the different size of the subarrays
	      return _sort(result);
	    }
	  });

	  // create subset
	  function _subset(array, bitarray) {
	    const result = [];
	    for (let i = 0; i < bitarray.length; i++) {
	      if (bitarray[i] === '1') {
	        result.push(array[i]);
	      }
	    }
	    return result;
	  }

	  // sort subsests by length
	  function _sort(array) {
	    let temp = [];
	    for (let i = array.length - 1; i > 0; i--) {
	      for (let j = 0; j < i; j++) {
	        if (array[j].length > array[j + 1].length) {
	          temp = array[j];
	          array[j] = array[j + 1];
	          array[j + 1] = temp;
	        }
	      }
	    }
	    return array;
	  }
	});
	return setPowerset$1;
}

var setSize$1 = {};

var hasRequiredSetSize$1;

function requireSetSize$1 () {
	if (hasRequiredSetSize$1) return setSize$1;
	hasRequiredSetSize$1 = 1;

	Object.defineProperty(setSize$1, "__esModule", {
	  value: true
	});
	setSize$1.createSetSize = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setSize';
	const dependencies = ['typed', 'compareNatural'];
	setSize$1.createSetSize = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    compareNatural
	  } = _ref;
	  /**
	   * Count the number of elements of a (multi)set. When a second parameter is 'true', count only the unique values.
	   * A multi-dimension array will be converted to a single-dimension array before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setSize(set)
	   *    math.setSize(set, unique)
	   *
	   * Examples:
	   *
	   *    math.setSize([1, 2, 2, 4])          // returns 4
	   *    math.setSize([1, 2, 2, 4], true)    // returns 3
	   *
	   * See also:
	   *
	   *    setUnion, setIntersect, setDifference
	   *
	   * @param {Array | Matrix} a  A multiset
	   * @param {boolean} [unique]  If true, only the unique values are counted. False by default
	   * @return {number}           The number of elements of the (multi)set
	   */
	  return typed(name, {
	    'Array | Matrix': function (a) {
	      return Array.isArray(a) ? (0, _array.flatten)(a).length : (0, _array.flatten)(a.toArray()).length;
	    },
	    'Array | Matrix, boolean': function (a, unique) {
	      if (unique === false || a.length === 0) {
	        return Array.isArray(a) ? (0, _array.flatten)(a).length : (0, _array.flatten)(a.toArray()).length;
	      } else {
	        const b = (0, _array.flatten)(Array.isArray(a) ? a : a.toArray()).sort(compareNatural);
	        let count = 1;
	        for (let i = 1; i < b.length; i++) {
	          if (compareNatural(b[i], b[i - 1]) !== 0) {
	            count++;
	          }
	        }
	        return count;
	      }
	    }
	  });
	});
	return setSize$1;
}

var setSymDifference$1 = {};

var hasRequiredSetSymDifference$1;

function requireSetSymDifference$1 () {
	if (hasRequiredSetSymDifference$1) return setSymDifference$1;
	hasRequiredSetSymDifference$1 = 1;

	Object.defineProperty(setSymDifference$1, "__esModule", {
	  value: true
	});
	setSymDifference$1.createSetSymDifference = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setSymDifference';
	const dependencies = ['typed', 'size', 'concat', 'subset', 'setDifference', 'Index'];
	setSymDifference$1.createSetSymDifference = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    concat,
	    subset,
	    setDifference,
	    Index
	  } = _ref;
	  /**
	   * Create the symmetric difference of two (multi)sets.
	   * Multi-dimension arrays will be converted to single-dimension arrays before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setSymDifference(set1, set2)
	   *
	   * Examples:
	   *
	   *    math.setSymDifference([1, 2, 3, 4], [3, 4, 5, 6])            // returns [1, 2, 5, 6]
	   *    math.setSymDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]])    // returns [1, 2, 5, 6]
	   *
	   * See also:
	   *
	   *    setUnion, setIntersect, setDifference
	   *
	   * @param {Array | Matrix}    a1  A (multi)set
	   * @param {Array | Matrix}    a2  A (multi)set
	   * @return {Array | Matrix}    The symmetric difference of two (multi)sets
	   */
	  return typed(name, {
	    'Array | Matrix, Array | Matrix': function (a1, a2) {
	      if (subset(size(a1), new Index(0)) === 0) {
	        // if any of them is empty, return the other one
	        return (0, _array.flatten)(a2);
	      } else if (subset(size(a2), new Index(0)) === 0) {
	        return (0, _array.flatten)(a1);
	      }
	      const b1 = (0, _array.flatten)(a1);
	      const b2 = (0, _array.flatten)(a2);
	      return concat(setDifference(b1, b2), setDifference(b2, b1));
	    }
	  });
	});
	return setSymDifference$1;
}

var setUnion$1 = {};

var hasRequiredSetUnion$1;

function requireSetUnion$1 () {
	if (hasRequiredSetUnion$1) return setUnion$1;
	hasRequiredSetUnion$1 = 1;

	Object.defineProperty(setUnion$1, "__esModule", {
	  value: true
	});
	setUnion$1.createSetUnion = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'setUnion';
	const dependencies = ['typed', 'size', 'concat', 'subset', 'setIntersect', 'setSymDifference', 'Index'];
	setUnion$1.createSetUnion = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    size,
	    concat,
	    subset,
	    setIntersect,
	    setSymDifference,
	    Index
	  } = _ref;
	  /**
	   * Create the union of two (multi)sets.
	   * Multi-dimension arrays will be converted to single-dimension arrays before the operation.
	   *
	   * Syntax:
	   *
	   *    math.setUnion(set1, set2)
	   *
	   * Examples:
	   *
	   *    math.setUnion([1, 2, 3, 4], [3, 4, 5, 6])            // returns [1, 2, 3, 4, 5, 6]
	   *    math.setUnion([[1, 2], [3, 4]], [[3, 4], [5, 6]])    // returns [1, 2, 3, 4, 5, 6]
	   *
	   * See also:
	   *
	   *    setIntersect, setDifference
	   *
	   * @param {Array | Matrix}    a1  A (multi)set
	   * @param {Array | Matrix}    a2  A (multi)set
	   * @return {Array | Matrix}    The union of two (multi)sets
	   */
	  return typed(name, {
	    'Array | Matrix, Array | Matrix': function (a1, a2) {
	      if (subset(size(a1), new Index(0)) === 0) {
	        // if any of them is empty, return the other one
	        return (0, _array.flatten)(a2);
	      } else if (subset(size(a2), new Index(0)) === 0) {
	        return (0, _array.flatten)(a1);
	      }
	      const b1 = (0, _array.flatten)(a1);
	      const b2 = (0, _array.flatten)(a2);
	      return concat(setSymDifference(b1, b2), setIntersect(b1, b2));
	    }
	  });
	});
	return setUnion$1;
}

var add$1 = {};

var hasRequiredAdd$1;

function requireAdd$1 () {
	if (hasRequiredAdd$1) return add$1;
	hasRequiredAdd$1 = 1;

	Object.defineProperty(add$1, "__esModule", {
	  value: true
	});
	add$1.createAdd = void 0;
	var _factory = requireFactory$b();
	var _matAlgo01xDSid = requireMatAlgo01xDSid();
	var _matAlgo04xSidSid = requireMatAlgo04xSidSid();
	var _matAlgo10xSids = requireMatAlgo10xSids();
	var _matrixAlgorithmSuite = requireMatrixAlgorithmSuite();
	const name = 'add';
	const dependencies = ['typed', 'matrix', 'addScalar', 'equalScalar', 'DenseMatrix', 'SparseMatrix', 'concat'];
	add$1.createAdd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    addScalar,
	    equalScalar,
	    DenseMatrix,
	    SparseMatrix,
	    concat
	  } = _ref;
	  const matAlgo01xDSid = (0, _matAlgo01xDSid.createMatAlgo01xDSid)({
	    typed
	  });
	  const matAlgo04xSidSid = (0, _matAlgo04xSidSid.createMatAlgo04xSidSid)({
	    typed,
	    equalScalar
	  });
	  const matAlgo10xSids = (0, _matAlgo10xSids.createMatAlgo10xSids)({
	    typed,
	    DenseMatrix
	  });
	  const matrixAlgorithmSuite = (0, _matrixAlgorithmSuite.createMatrixAlgorithmSuite)({
	    typed,
	    matrix,
	    concat
	  });
	  /**
	   * Add two or more values, `x + y`.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.add(x, y)
	   *    math.add(x, y, z, ...)
	   *
	   * Examples:
	   *
	   *    math.add(2, 3)               // returns number 5
	   *    math.add(2, 3, 4)            // returns number 9
	   *
	   *    const a = math.complex(2, 3)
	   *    const b = math.complex(-4, 1)
	   *    math.add(a, b)               // returns Complex -2 + 4i
	   *
	   *    math.add([1, 2, 3], 4)       // returns Array [5, 6, 7]
	   *
	   *    const c = math.unit('5 cm')
	   *    const d = math.unit('2.1 mm')
	   *    math.add(c, d)               // returns Unit 5.21 cm
	   *
	   *    math.add("2.3", "4")         // returns number 6.3
	   *
	   * See also:
	   *
	   *    subtract, sum
	   *
	   * History:
	   *
	   *     v13     Handle bigint arguments
	   *     v11.6   Support matrix broadcasting
	   *     v3.8    Allow more than two arguments
	   *     v0.0.2  Created
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} x First value to add
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} y Second value to add
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} Sum of `x` and `y`
	   */
	  return typed(name, {
	    'any, any': addScalar,
	    'any, any, ...any': typed.referToSelf(self => (x, y, rest) => {
	      let result = self(x, y);
	      for (let i = 0; i < rest.length; i++) {
	        result = self(result, rest[i]);
	      }
	      return result;
	    })
	  }, matrixAlgorithmSuite({
	    elop: addScalar,
	    DS: matAlgo01xDSid,
	    SS: matAlgo04xSidSid,
	    Ss: matAlgo10xSids
	  }));
	});
	return add$1;
}

var hypot$1 = {};

var hasRequiredHypot$1;

function requireHypot$1 () {
	if (hasRequiredHypot$1) return hypot$1;
	hasRequiredHypot$1 = 1;

	Object.defineProperty(hypot$1, "__esModule", {
	  value: true
	});
	hypot$1.createHypot = void 0;
	var _factory = requireFactory$b();
	var _array = requireArray();
	var _is = requireIs();
	const name = 'hypot';
	const dependencies = ['typed', 'abs', 'addScalar', 'divideScalar', 'multiplyScalar', 'sqrt', 'smaller', 'isPositive'];
	hypot$1.createHypot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    abs,
	    addScalar,
	    divideScalar,
	    multiplyScalar,
	    sqrt,
	    smaller,
	    isPositive
	  } = _ref;
	  /**
	   * Calculate the hypotenuse of a list with values. The hypotenuse is defined as:
	   *
	   *     hypot(a, b, c, ...) = sqrt(a^2 + b^2 + c^2 + ...)
	   *
	   * For matrix input, the hypotenuse is calculated for all values in the matrix.
	   *
	   * Syntax:
	   *
	   *     math.hypot(a, b, ...)
	   *     math.hypot([a, b, c, ...])
	   *
	   * Examples:
	   *
	   *     math.hypot(3, 4)      // 5
	   *     math.hypot(3, 4, 5)   // 7.0710678118654755
	   *     math.hypot([3, 4, 5]) // 7.0710678118654755
	   *     math.hypot(-2)        // 2
	   *
	   * See also:
	   *
	   *     abs, norm
	   *
	   * @param {... number | BigNumber | Array | Matrix} args    A list with numeric values or an Array or Matrix.
	   *                                                          Matrix and Array input is flattened and returns a
	   *                                                          single number for the whole matrix.
	   * @return {number | BigNumber} Returns the hypothenusa of the input values.
	   */
	  return typed(name, {
	    '... number | BigNumber': _hypot,
	    Array: _hypot,
	    Matrix: M => _hypot((0, _array.flatten)(M.toArray(), true))
	  });

	  /**
	   * Calculate the hypotenuse for an Array with values
	   * @param {Array.<number | BigNumber>} args
	   * @return {number | BigNumber} Returns the result
	   * @private
	   */
	  function _hypot(args) {
	    // code based on `hypot` from es6-shim:
	    // https://github.com/paulmillr/es6-shim/blob/master/es6-shim.js#L1619-L1633
	    let result = 0;
	    let largest = 0;
	    for (let i = 0; i < args.length; i++) {
	      if ((0, _is.isComplex)(args[i])) {
	        throw new TypeError('Unexpected type of argument to hypot');
	      }
	      const value = abs(args[i]);
	      if (smaller(largest, value)) {
	        result = multiplyScalar(result, multiplyScalar(divideScalar(largest, value), divideScalar(largest, value)));
	        result = addScalar(result, 1);
	        largest = value;
	      } else {
	        result = addScalar(result, isPositive(value) ? multiplyScalar(divideScalar(value, largest), divideScalar(value, largest)) : value);
	      }
	    }
	    return multiplyScalar(largest, sqrt(result));
	  }
	});
	return hypot$1;
}

var norm$1 = {};

var hasRequiredNorm$1;

function requireNorm$1 () {
	if (hasRequiredNorm$1) return norm$1;
	hasRequiredNorm$1 = 1;

	Object.defineProperty(norm$1, "__esModule", {
	  value: true
	});
	norm$1.createNorm = void 0;
	var _factory = requireFactory$b();
	const name = 'norm';
	const dependencies = ['typed', 'abs', 'add', 'pow', 'conj', 'sqrt', 'multiply', 'equalScalar', 'larger', 'smaller', 'matrix', 'ctranspose', 'eigs'];
	norm$1.createNorm = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    abs,
	    add,
	    pow,
	    conj,
	    sqrt,
	    multiply,
	    equalScalar,
	    larger,
	    smaller,
	    matrix,
	    ctranspose,
	    eigs
	  } = _ref;
	  /**
	   * Calculate the norm of a number, vector or matrix.
	   *
	   * The second parameter p is optional. If not provided, it defaults to 2.
	   *
	   * Syntax:
	   *
	   *    math.norm(x)
	   *    math.norm(x, p)
	   *
	   * Examples:
	   *
	   *    math.abs(-3.5)                         // returns 3.5
	   *    math.norm(-3.5)                        // returns 3.5
	   *
	   *    math.norm(math.complex(3, -4))         // returns 5
	   *
	   *    math.norm([1, 2, -3], Infinity)        // returns 3
	   *    math.norm([1, 2, -3], -Infinity)       // returns 1
	   *
	   *    math.norm([3, 4], 2)                   // returns 5
	   *
	   *    math.norm([[1, 2], [3, 4]], 1)          // returns 6
	   *    math.norm([[1, 2], [3, 4]], 'inf')     // returns 7
	   *    math.norm([[1, 2], [3, 4]], 'fro')     // returns 5.477225575051661
	   *
	   * See also:
	   *
	   *    abs, hypot
	   *
	   * @param  {number | BigNumber | Complex | Array | Matrix} x
	   *            Value for which to calculate the norm
	   * @param  {number | BigNumber | string} [p=2]
	   *            Vector space.
	   *            Supported numbers include Infinity and -Infinity.
	   *            Supported strings are: 'inf', '-inf', and 'fro' (The Frobenius norm)
	   * @return {number | BigNumber} the p-norm
	   */
	  return typed(name, {
	    number: Math.abs,
	    Complex: function (x) {
	      return x.abs();
	    },
	    BigNumber: function (x) {
	      // norm(x) = abs(x)
	      return x.abs();
	    },
	    boolean: function (x) {
	      // norm(x) = abs(x)
	      return Math.abs(x);
	    },
	    Array: function (x) {
	      return _norm(matrix(x), 2);
	    },
	    Matrix: function (x) {
	      return _norm(x, 2);
	    },
	    'Array, number | BigNumber | string': function (x, p) {
	      return _norm(matrix(x), p);
	    },
	    'Matrix, number | BigNumber | string': function (x, p) {
	      return _norm(x, p);
	    }
	  });

	  /**
	   * Calculate the plus infinity norm for a vector
	   * @param {Matrix} x
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _vectorNormPlusInfinity(x) {
	    // norm(x, Infinity) = max(abs(x))
	    let pinf = 0;
	    // skip zeros since abs(0) === 0
	    x.forEach(function (value) {
	      const v = abs(value);
	      if (larger(v, pinf)) {
	        pinf = v;
	      }
	    }, true);
	    return pinf;
	  }

	  /**
	   * Calculate the minus infinity norm for a vector
	   * @param {Matrix} x
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _vectorNormMinusInfinity(x) {
	    // norm(x, -Infinity) = min(abs(x))
	    let ninf;
	    // skip zeros since abs(0) === 0
	    x.forEach(function (value) {
	      const v = abs(value);
	      if (!ninf || smaller(v, ninf)) {
	        ninf = v;
	      }
	    }, true);
	    return ninf || 0;
	  }

	  /**
	   * Calculate the norm for a vector
	   * @param {Matrix} x
	   * @param {number | string} p
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _vectorNorm(x, p) {
	    // check p
	    if (p === Number.POSITIVE_INFINITY || p === 'inf') {
	      return _vectorNormPlusInfinity(x);
	    }
	    if (p === Number.NEGATIVE_INFINITY || p === '-inf') {
	      return _vectorNormMinusInfinity(x);
	    }
	    if (p === 'fro') {
	      return _norm(x, 2);
	    }
	    if (typeof p === 'number' && !isNaN(p)) {
	      // check p != 0
	      if (!equalScalar(p, 0)) {
	        // norm(x, p) = sum(abs(xi) ^ p) ^ 1/p
	        let n = 0;
	        // skip zeros since abs(0) === 0
	        x.forEach(function (value) {
	          n = add(pow(abs(value), p), n);
	        }, true);
	        return pow(n, 1 / p);
	      }
	      return Number.POSITIVE_INFINITY;
	    }
	    // invalid parameter value
	    throw new Error('Unsupported parameter value');
	  }

	  /**
	   * Calculate the Frobenius norm for a matrix
	   * @param {Matrix} x
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _matrixNormFrobenius(x) {
	    // norm(x) = sqrt(sum(diag(x'x)))
	    let fro = 0;
	    x.forEach(function (value, index) {
	      fro = add(fro, multiply(value, conj(value)));
	    });
	    return abs(sqrt(fro));
	  }

	  /**
	   * Calculate the norm L1 for a matrix
	   * @param {Matrix} x
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _matrixNormOne(x) {
	    // norm(x) = the largest column sum
	    const c = [];
	    // result
	    let maxc = 0;
	    // skip zeros since abs(0) == 0
	    x.forEach(function (value, index) {
	      const j = index[1];
	      const cj = add(c[j] || 0, abs(value));
	      if (larger(cj, maxc)) {
	        maxc = cj;
	      }
	      c[j] = cj;
	    }, true);
	    return maxc;
	  }

	  /**
	   * Calculate the norm L2 for a matrix
	   * @param {Matrix} x
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _matrixNormTwo(x) {
	    // norm(x) = sqrt( max eigenvalue of A*.A)
	    const sizeX = x.size();
	    if (sizeX[0] !== sizeX[1]) {
	      throw new RangeError('Invalid matrix dimensions');
	    }
	    const tx = ctranspose(x);
	    const squaredX = multiply(tx, x);
	    const eigenVals = eigs(squaredX).values.toArray();
	    const rho = eigenVals[eigenVals.length - 1];
	    return abs(sqrt(rho));
	  }

	  /**
	   * Calculate the infinity norm for a matrix
	   * @param {Matrix} x
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _matrixNormInfinity(x) {
	    // norm(x) = the largest row sum
	    const r = [];
	    // result
	    let maxr = 0;
	    // skip zeros since abs(0) == 0
	    x.forEach(function (value, index) {
	      const i = index[0];
	      const ri = add(r[i] || 0, abs(value));
	      if (larger(ri, maxr)) {
	        maxr = ri;
	      }
	      r[i] = ri;
	    }, true);
	    return maxr;
	  }

	  /**
	   * Calculate the norm for a 2D Matrix (M*N)
	   * @param {Matrix} x
	   * @param {number | string} p
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _matrixNorm(x, p) {
	    // check p
	    if (p === 1) {
	      return _matrixNormOne(x);
	    }
	    if (p === Number.POSITIVE_INFINITY || p === 'inf') {
	      return _matrixNormInfinity(x);
	    }
	    if (p === 'fro') {
	      return _matrixNormFrobenius(x);
	    }
	    if (p === 2) {
	      return _matrixNormTwo(x);
	    } // invalid parameter value

	    throw new Error('Unsupported parameter value ' + p);
	  }

	  /**
	   * Calculate the norm for an array
	   * @param {Matrix} x
	   * @param {number | string} p
	   * @returns {number} Returns the norm
	   * @private
	   */
	  function _norm(x, p) {
	    // size
	    const sizeX = x.size();

	    // check if it is a vector
	    if (sizeX.length === 1) {
	      return _vectorNorm(x, p);
	    }
	    // MxN matrix
	    if (sizeX.length === 2) {
	      if (sizeX[0] && sizeX[1]) {
	        return _matrixNorm(x, p);
	      } else {
	        throw new RangeError('Invalid matrix dimensions');
	      }
	    }
	  }
	});
	return norm$1;
}

var dot$1 = {};

var hasRequiredDot$1;

function requireDot$1 () {
	if (hasRequiredDot$1) return dot$1;
	hasRequiredDot$1 = 1;

	Object.defineProperty(dot$1, "__esModule", {
	  value: true
	});
	dot$1.createDot = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'dot';
	const dependencies = ['typed', 'addScalar', 'multiplyScalar', 'conj', 'size'];
	dot$1.createDot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    addScalar,
	    multiplyScalar,
	    conj,
	    size
	  } = _ref;
	  /**
	   * Calculate the dot product of two vectors. The dot product of
	   * `A = [a1, a2, ..., an]` and `B = [b1, b2, ..., bn]` is defined as:
	   *
	   *    dot(A, B) = conj(a1) * b1 + conj(a2) * b2 + ... + conj(an) * bn
	   *
	   * Syntax:
	   *
	   *    math.dot(x, y)
	   *
	   * Examples:
	   *
	   *    math.dot([2, 4, 1], [2, 2, 3])       // returns number 15
	   *    math.multiply([2, 4, 1], [2, 2, 3])  // returns number 15
	   *
	   * See also:
	   *
	   *    multiply, cross
	   *
	   * @param  {Array | Matrix} x     First vector
	   * @param  {Array | Matrix} y     Second vector
	   * @return {number}               Returns the dot product of `x` and `y`
	   */
	  return typed(name, {
	    'Array | DenseMatrix, Array | DenseMatrix': _denseDot,
	    'SparseMatrix, SparseMatrix': _sparseDot
	  });
	  function _validateDim(x, y) {
	    const xSize = size(x);
	    const ySize = size(y);
	    let xLen, yLen;
	    if (xSize.length === 1) {
	      xLen = xSize[0];
	    } else if (xSize.length === 2 && xSize[1] === 1) {
	      xLen = xSize[0];
	    } else {
	      throw new RangeError('Expected a column vector, instead got a matrix of size (' + xSize.join(', ') + ')');
	    }
	    if (ySize.length === 1) {
	      yLen = ySize[0];
	    } else if (ySize.length === 2 && ySize[1] === 1) {
	      yLen = ySize[0];
	    } else {
	      throw new RangeError('Expected a column vector, instead got a matrix of size (' + ySize.join(', ') + ')');
	    }
	    if (xLen !== yLen) throw new RangeError('Vectors must have equal length (' + xLen + ' != ' + yLen + ')');
	    if (xLen === 0) throw new RangeError('Cannot calculate the dot product of empty vectors');
	    return xLen;
	  }
	  function _denseDot(a, b) {
	    const N = _validateDim(a, b);
	    const adata = (0, _is.isMatrix)(a) ? a._data : a;
	    const adt = (0, _is.isMatrix)(a) ? a._datatype || a.getDataType() : undefined;
	    const bdata = (0, _is.isMatrix)(b) ? b._data : b;
	    const bdt = (0, _is.isMatrix)(b) ? b._datatype || b.getDataType() : undefined;

	    // are these 2-dimensional column vectors? (as opposed to 1-dimensional vectors)
	    const aIsColumn = size(a).length === 2;
	    const bIsColumn = size(b).length === 2;
	    let add = addScalar;
	    let mul = multiplyScalar;

	    // process data types
	    if (adt && bdt && adt === bdt && typeof adt === 'string' && adt !== 'mixed') {
	      const dt = adt;
	      // find signatures that matches (dt, dt)
	      add = typed.find(addScalar, [dt, dt]);
	      mul = typed.find(multiplyScalar, [dt, dt]);
	    }

	    // both vectors 1-dimensional
	    if (!aIsColumn && !bIsColumn) {
	      let c = mul(conj(adata[0]), bdata[0]);
	      for (let i = 1; i < N; i++) {
	        c = add(c, mul(conj(adata[i]), bdata[i]));
	      }
	      return c;
	    }

	    // a is 1-dim, b is column
	    if (!aIsColumn && bIsColumn) {
	      let c = mul(conj(adata[0]), bdata[0][0]);
	      for (let i = 1; i < N; i++) {
	        c = add(c, mul(conj(adata[i]), bdata[i][0]));
	      }
	      return c;
	    }

	    // a is column, b is 1-dim
	    if (aIsColumn && !bIsColumn) {
	      let c = mul(conj(adata[0][0]), bdata[0]);
	      for (let i = 1; i < N; i++) {
	        c = add(c, mul(conj(adata[i][0]), bdata[i]));
	      }
	      return c;
	    }

	    // both vectors are column
	    if (aIsColumn && bIsColumn) {
	      let c = mul(conj(adata[0][0]), bdata[0][0]);
	      for (let i = 1; i < N; i++) {
	        c = add(c, mul(conj(adata[i][0]), bdata[i][0]));
	      }
	      return c;
	    }
	  }
	  function _sparseDot(x, y) {
	    _validateDim(x, y);
	    const xindex = x._index;
	    const xvalues = x._values;
	    const yindex = y._index;
	    const yvalues = y._values;

	    // TODO optimize add & mul using datatype
	    let c = 0;
	    const add = addScalar;
	    const mul = multiplyScalar;
	    let i = 0;
	    let j = 0;
	    while (i < xindex.length && j < yindex.length) {
	      const I = xindex[i];
	      const J = yindex[j];
	      if (I < J) {
	        i++;
	        continue;
	      }
	      if (I > J) {
	        j++;
	        continue;
	      }
	      if (I === J) {
	        c = add(c, mul(xvalues[i], yvalues[j]));
	        i++;
	        j++;
	      }
	    }
	    return c;
	  }
	});
	return dot$1;
}

var trace$1 = {};

var hasRequiredTrace$1;

function requireTrace$1 () {
	if (hasRequiredTrace$1) return trace$1;
	hasRequiredTrace$1 = 1;

	Object.defineProperty(trace$1, "__esModule", {
	  value: true
	});
	trace$1.createTrace = void 0;
	var _object = requireObject$3();
	var _string = requireString$2();
	var _factory = requireFactory$b();
	const name = 'trace';
	const dependencies = ['typed', 'matrix', 'add'];
	trace$1.createTrace = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    add
	  } = _ref;
	  /**
	   * Calculate the trace of a matrix: the sum of the elements on the main
	   * diagonal of a square matrix.
	   *
	   * Syntax:
	   *
	   *    math.trace(x)
	   *
	   * Examples:
	   *
	   *    math.trace([[1, 2], [3, 4]]) // returns 5
	   *
	   *    const A = [
	   *      [1, 2, 3],
	   *      [-1, 2, 3],
	   *      [2, 0, 3]
	   *    ]
	   *    math.trace(A) // returns 6
	   *
	   * See also:
	   *
	   *    diag
	   *
	   * @param {Array | Matrix} x  A matrix
	   *
	   * @return {number} The trace of `x`
	   */
	  return typed('trace', {
	    Array: function _arrayTrace(x) {
	      // use dense matrix implementation
	      return _denseTrace(matrix(x));
	    },
	    SparseMatrix: _sparseTrace,
	    DenseMatrix: _denseTrace,
	    any: _object.clone
	  });
	  function _denseTrace(m) {
	    // matrix size & data
	    const size = m._size;
	    const data = m._data;

	    // process dimensions
	    switch (size.length) {
	      case 1:
	        // vector
	        if (size[0] === 1) {
	          // return data[0]
	          return (0, _object.clone)(data[0]);
	        }
	        throw new RangeError('Matrix must be square (size: ' + (0, _string.format)(size) + ')');
	      case 2:
	        {
	          // two dimensional
	          const rows = size[0];
	          const cols = size[1];
	          if (rows === cols) {
	            // calulate sum
	            let sum = 0;
	            // loop diagonal
	            for (let i = 0; i < rows; i++) {
	              sum = add(sum, data[i][i]);
	            }
	            // return trace
	            return sum;
	          } else {
	            throw new RangeError('Matrix must be square (size: ' + (0, _string.format)(size) + ')');
	          }
	        }
	      default:
	        // multi dimensional
	        throw new RangeError('Matrix must be two dimensional (size: ' + (0, _string.format)(size) + ')');
	    }
	  }
	  function _sparseTrace(m) {
	    // matrix arrays
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;
	    const size = m._size;
	    // check dimensions
	    const rows = size[0];
	    const columns = size[1];
	    // matrix must be square
	    if (rows === columns) {
	      // calulate sum
	      let sum = 0;
	      // check we have data (avoid looping columns)
	      if (values.length > 0) {
	        // loop columns
	        for (let j = 0; j < columns; j++) {
	          // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	          const k0 = ptr[j];
	          const k1 = ptr[j + 1];
	          // loop k within [k0, k1[
	          for (let k = k0; k < k1; k++) {
	            // row index
	            const i = index[k];
	            // check row
	            if (i === j) {
	              // accumulate value
	              sum = add(sum, values[k]);
	              // exit loop
	              break;
	            }
	            if (i > j) {
	              // exit loop, no value on the diagonal for column j
	              break;
	            }
	          }
	        }
	      }
	      // return trace
	      return sum;
	    }
	    throw new RangeError('Matrix must be square (size: ' + (0, _string.format)(size) + ')');
	  }
	});
	return trace$1;
}

var _function = {};

var hasRequired_function;

function require_function () {
	if (hasRequired_function) return _function;
	hasRequired_function = 1;

	Object.defineProperty(_function, "__esModule", {
	  value: true
	});
	_function.createIndex = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'index';
	const dependencies = ['typed', 'Index'];
	_function.createIndex = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Index
	  } = _ref;
	  /**
	   * Create an index. An Index can store ranges having start, step, and end
	   * for multiple dimensions.
	   * Matrix.get, Matrix.set, and math.subset accept an Index as input.
	   *
	   * Syntax:
	   *
	   *     math.index(range1, range2, ...)
	   *
	   * Where each range can be any of:
	   *
	   * - A number
	   * - A string for getting/setting an object property
	   * - An instance of `Range`
	   * - A one-dimensional Array or a Matrix with numbers or booleans
	   *
	   * Indexes must be zero-based, integer numbers.
	   *
	   * Examples:
	   *
	   *    const b = [1, 2, 3, 4, 5]
	   *    math.subset(b, math.index([1, 2, 3]))                         // returns [2, 3, 4]
	   *    math.subset(b, math.index([false, true, true, true, false]))  // returns [2, 3, 4]
	   *
	   *    const a = math.matrix([[1, 2], [3, 4]])
	   *    a.subset(math.index(0, 1))             // returns 2
	   *    a.subset(math.index(0, [false, true])) // returns 2
	   *
	   * See also:
	   *
	   *    bignumber, boolean, complex, matrix, number, string, unit
	   *
	   * History:
	   *
	   *    v?      Created
	   *    v2      Dropped support for `[start, end, step]` arguments in favor
	   *            of lists of arbitrary values; added support for Range
	   *            objects.
	   *    v11.10  Added support for arrays of booleans as indices.
	   *
	   * @param {...*} ranges   Zero or more ranges or numbers.
	   * @return {Index}        Returns the created index
	   */
	  return typed(name, {
	    '...number | string | BigNumber | Range | Array | Matrix': function (args) {
	      const ranges = args.map(function (arg) {
	        if ((0, _is.isBigNumber)(arg)) {
	          return arg.toNumber(); // convert BigNumber to Number
	        } else if ((0, _is.isArray)(arg) || (0, _is.isMatrix)(arg)) {
	          return arg.map(function (elem) {
	            // convert BigNumber to Number
	            return (0, _is.isBigNumber)(elem) ? elem.toNumber() : elem;
	          });
	        } else {
	          return arg;
	        }
	      });
	      const res = new Index();
	      Index.apply(res, ranges);
	      return res;
	    }
	  });
	});
	return _function;
}

var Node$1 = {};

var keywords = {};

var hasRequiredKeywords;

function requireKeywords () {
	if (hasRequiredKeywords) return keywords;
	hasRequiredKeywords = 1;

	Object.defineProperty(keywords, "__esModule", {
	  value: true
	});
	keywords.keywords = void 0;
	// Reserved keywords not allowed to use in the parser
	keywords.keywords = new Set(['end']);
	return keywords;
}

var hasRequiredNode;

function requireNode () {
	if (hasRequiredNode) return Node$1;
	hasRequiredNode = 1;

	Object.defineProperty(Node$1, "__esModule", {
	  value: true
	});
	Node$1.createNode = void 0;
	var _is = requireIs();
	var _keywords = requireKeywords();
	var _object = requireObject$3();
	var _factory = requireFactory$b();
	var _map = requireMap$2();
	const name = 'Node';
	const dependencies = ['mathWithTransform'];
	Node$1.createNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    mathWithTransform
	  } = _ref;
	  /**
	   * Validate the symbol names of a scope.
	   * Throws an error when the scope contains an illegal symbol.
	   * @param {Object} scope
	   */
	  function _validateScope(scope) {
	    for (const symbol of [..._keywords.keywords]) {
	      if (scope.has(symbol)) {
	        throw new Error('Scope contains an illegal symbol, "' + symbol + '" is a reserved keyword');
	      }
	    }
	  }
	  class Node {
	    get type() {
	      return 'Node';
	    }
	    get isNode() {
	      return true;
	    }

	    /**
	     * Evaluate the node
	     * @param {Object} [scope]  Scope to read/write variables
	     * @return {*}              Returns the result
	     */
	    evaluate(scope) {
	      return this.compile().evaluate(scope);
	    }

	    /**
	     * Compile the node into an optimized, evauatable JavaScript function
	     * @return {{evaluate: function([Object])}} object
	     *                Returns an object with a function 'evaluate',
	     *                which can be invoked as expr.evaluate([scope: Object]),
	     *                where scope is an optional object with
	     *                variables.
	     */
	    compile() {
	      const expr = this._compile(mathWithTransform, {});
	      const args = {};
	      const context = null;
	      function evaluate(scope) {
	        const s = (0, _map.createMap)(scope);
	        _validateScope(s);
	        return expr(s, args, context);
	      }
	      return {
	        evaluate
	      };
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      throw new Error('Method _compile must be implemented by type ' + this.type);
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      // must be implemented by each of the Node implementations
	      throw new Error('Cannot run forEach on a Node interface');
	    }

	    /**
	     * Create a new Node whose children are the results of calling the
	     * provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {OperatorNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      // must be implemented by each of the Node implementations
	      throw new Error('Cannot run map on a Node interface');
	    }

	    /**
	     * Validate whether an object is a Node, for use with map
	     * @param {Node} node
	     * @returns {Node} Returns the input if it's a node, else throws an Error
	     * @protected
	     */
	    _ifNode(node) {
	      if (!(0, _is.isNode)(node)) {
	        throw new TypeError('Callback function must return a Node');
	      }
	      return node;
	    }

	    /**
	     * Recursively traverse all nodes in a node tree. Executes given callback for
	     * this node and each of its child nodes.
	     * @param {function(node: Node, path: string, parent: Node)} callback
	     *          A callback called for every node in the node tree.
	     */
	    traverse(callback) {
	      // execute callback for itself
	      // eslint-disable-next-line
	      callback(this, null, null);

	      // recursively traverse over all children of a node
	      function _traverse(node, callback) {
	        node.forEach(function (child, path, parent) {
	          callback(child, path, parent);
	          _traverse(child, callback);
	        });
	      }
	      _traverse(this, callback);
	    }

	    /**
	     * Recursively transform a node tree via a transform function.
	     *
	     * For example, to replace all nodes of type SymbolNode having name 'x' with
	     * a ConstantNode with value 2:
	     *
	     *     const res = Node.transform(function (node, path, parent) {
	     *       if (node && node.isSymbolNode) && (node.name === 'x')) {
	     *         return new ConstantNode(2)
	     *       }
	     *       else {
	     *         return node
	     *       }
	     *     })
	     *
	     * @param {function(node: Node, path: string, parent: Node) : Node} callback
	     *          A mapping function accepting a node, and returning
	     *          a replacement for the node or the original node. The "signature"
	     *          of the callback must be:
	     *          callback(node: Node, index: string, parent: Node) : Node
	     * @return {Node} Returns the original node or its replacement
	     */
	    transform(callback) {
	      function _transform(child, path, parent) {
	        const replacement = callback(child, path, parent);
	        if (replacement !== child) {
	          // stop iterating when the node is replaced
	          return replacement;
	        }
	        return child.map(_transform);
	      }
	      return _transform(this, null, null);
	    }

	    /**
	     * Find any node in the node tree matching given filter function. For
	     * example, to find all nodes of type SymbolNode having name 'x':
	     *
	     *     const results = Node.filter(function (node) {
	     *       return (node && node.isSymbolNode) && (node.name === 'x')
	     *     })
	     *
	     * @param {function(node: Node, path: string, parent: Node) : Node} callback
	     *            A test function returning true when a node matches, and false
	     *            otherwise. Function signature:
	     *            callback(node: Node, index: string, parent: Node) : boolean
	     * @return {Node[]} nodes
	     *            An array with nodes matching given filter criteria
	     */
	    filter(callback) {
	      const nodes = [];
	      this.traverse(function (node, path, parent) {
	        if (callback(node, path, parent)) {
	          nodes.push(node);
	        }
	      });
	      return nodes;
	    }

	    /**
	     * Create a shallow clone of this node
	     * @return {Node}
	     */
	    clone() {
	      // must be implemented by each of the Node implementations
	      throw new Error('Cannot clone a Node interface');
	    }

	    /**
	     * Create a deep clone of this node
	     * @return {Node}
	     */
	    cloneDeep() {
	      return this.map(function (node) {
	        return node.cloneDeep();
	      });
	    }

	    /**
	     * Deep compare this node with another node.
	     * @param {Node} other
	     * @return {boolean} Returns true when both nodes are of the same type and
	     *                   contain the same values (as do their childs)
	     */
	    equals(other) {
	      return other ? this.type === other.type && (0, _object.deepStrictEqual)(this, other) : false;
	    }

	    /**
	     * Get string representation. (wrapper function)
	     *
	     * This function can get an object of the following form:
	     * {
	     *    handler: //This can be a callback function of the form
	     *             // "function callback(node, options)"or
	     *             // a map that maps function names (used in FunctionNodes)
	     *             // to callbacks
	     *    parenthesis: "keep" //the parenthesis option (This is optional)
	     * }
	     *
	     * @param {Object} [options]
	     * @return {string}
	     */
	    toString(options) {
	      const customString = this._getCustomString(options);
	      if (typeof customString !== 'undefined') {
	        return customString;
	      }
	      return this._toString(options);
	    }

	    /**
	     * Internal function to generate the string output.
	     * This has to be implemented by every Node
	     *
	     * @throws {Error}
	     */
	    _toString() {
	      // must be implemented by each of the Node implementations
	      throw new Error('_toString not implemented for ' + this.type);
	    }

	    /**
	     * Get a JSON representation of the node
	     * Both .toJSON() and the static .fromJSON(json) should be implemented by all
	     * implementations of Node
	     * @returns {Object}
	     */
	    toJSON() {
	      throw new Error('Cannot serialize object: toJSON not implemented by ' + this.type);
	    }

	    /**
	     * Get HTML representation. (wrapper function)
	     *
	     * This function can get an object of the following form:
	     * {
	     *    handler: //This can be a callback function of the form
	     *             // "function callback(node, options)" or
	     *             // a map that maps function names (used in FunctionNodes)
	     *             // to callbacks
	     *    parenthesis: "keep" //the parenthesis option (This is optional)
	     * }
	     *
	     * @param {Object} [options]
	     * @return {string}
	     */
	    toHTML(options) {
	      const customString = this._getCustomString(options);
	      if (typeof customString !== 'undefined') {
	        return customString;
	      }
	      return this._toHTML(options);
	    }

	    /**
	     * Internal function to generate the HTML output.
	     * This has to be implemented by every Node
	     *
	     * @throws {Error}
	     */
	    _toHTML() {
	      // must be implemented by each of the Node implementations
	      throw new Error('_toHTML not implemented for ' + this.type);
	    }

	    /**
	     * Get LaTeX representation. (wrapper function)
	     *
	     * This function can get an object of the following form:
	     * {
	     *    handler: //This can be a callback function of the form
	     *             // "function callback(node, options)"or
	     *             // a map that maps function names (used in FunctionNodes)
	     *             // to callbacks
	     *    parenthesis: "keep" //the parenthesis option (This is optional)
	     * }
	     *
	     * @param {Object} [options]
	     * @return {string}
	     */
	    toTex(options) {
	      const customString = this._getCustomString(options);
	      if (typeof customString !== 'undefined') {
	        return customString;
	      }
	      return this._toTex(options);
	    }

	    /**
	     * Internal function to generate the LaTeX output.
	     * This has to be implemented by every Node
	     *
	     * @param {Object} [options]
	     * @throws {Error}
	     */
	    _toTex(options) {
	      // must be implemented by each of the Node implementations
	      throw new Error('_toTex not implemented for ' + this.type);
	    }

	    /**
	     * Helper used by `to...` functions.
	     */
	    _getCustomString(options) {
	      if (options && typeof options === 'object') {
	        switch (typeof options.handler) {
	          case 'object':
	          case 'undefined':
	            return;
	          case 'function':
	            return options.handler(this, options);
	          default:
	            throw new TypeError('Object or function expected as callback');
	        }
	      }
	    }

	    /**
	     * Get identifier.
	     * @return {string}
	     */
	    getIdentifier() {
	      return this.type;
	    }

	    /**
	     * Get the content of the current Node.
	     * @return {Node} node
	     **/
	    getContent() {
	      return this;
	    }
	  }
	  return Node;
	}, {
	  isClass: true,
	  isNode: true
	});
	return Node$1;
}

var AccessorNode = {};

var defineProperty = {exports: {}};

var toPropertyKey = {exports: {}};

var _typeof = {exports: {}};

_typeof.exports;

var hasRequired_typeof;

function require_typeof () {
	if (hasRequired_typeof) return _typeof.exports;
	hasRequired_typeof = 1;
	(function (module) {
		function _typeof(o) {
		  "@babel/helpers - typeof";

		  return module.exports = _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
		    return typeof o;
		  } : function (o) {
		    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
		  }, module.exports.__esModule = true, module.exports["default"] = module.exports, _typeof(o);
		}
		module.exports = _typeof, module.exports.__esModule = true, module.exports["default"] = module.exports; 
	} (_typeof));
	return _typeof.exports;
}

var toPrimitive = {exports: {}};

toPrimitive.exports;

var hasRequiredToPrimitive;

function requireToPrimitive () {
	if (hasRequiredToPrimitive) return toPrimitive.exports;
	hasRequiredToPrimitive = 1;
	(function (module) {
		var _typeof = require_typeof()["default"];
		function toPrimitive(t, r) {
		  if ("object" != _typeof(t) || !t) return t;
		  var e = t[Symbol.toPrimitive];
		  if (void 0 !== e) {
		    var i = e.call(t, r || "default");
		    if ("object" != _typeof(i)) return i;
		    throw new TypeError("@@toPrimitive must return a primitive value.");
		  }
		  return ("string" === r ? String : Number)(t);
		}
		module.exports = toPrimitive, module.exports.__esModule = true, module.exports["default"] = module.exports; 
	} (toPrimitive));
	return toPrimitive.exports;
}

toPropertyKey.exports;

var hasRequiredToPropertyKey;

function requireToPropertyKey () {
	if (hasRequiredToPropertyKey) return toPropertyKey.exports;
	hasRequiredToPropertyKey = 1;
	(function (module) {
		var _typeof = require_typeof()["default"];
		var toPrimitive = requireToPrimitive();
		function toPropertyKey(t) {
		  var i = toPrimitive(t, "string");
		  return "symbol" == _typeof(i) ? i : i + "";
		}
		module.exports = toPropertyKey, module.exports.__esModule = true, module.exports["default"] = module.exports; 
	} (toPropertyKey));
	return toPropertyKey.exports;
}

defineProperty.exports;

var hasRequiredDefineProperty;

function requireDefineProperty () {
	if (hasRequiredDefineProperty) return defineProperty.exports;
	hasRequiredDefineProperty = 1;
	(function (module) {
		var toPropertyKey = requireToPropertyKey();
		function _defineProperty(e, r, t) {
		  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		    value: t,
		    enumerable: !0,
		    configurable: !0,
		    writable: !0
		  }) : e[r] = t, e;
		}
		module.exports = _defineProperty, module.exports.__esModule = true, module.exports["default"] = module.exports; 
	} (defineProperty));
	return defineProperty.exports;
}

var access = {};

var errorTransform = {};

var hasRequiredErrorTransform;

function requireErrorTransform () {
	if (hasRequiredErrorTransform) return errorTransform;
	hasRequiredErrorTransform = 1;

	Object.defineProperty(errorTransform, "__esModule", {
	  value: true
	});
	errorTransform.errorTransform = errorTransform$1;
	var _IndexError = requireIndexError();
	/**
	 * Transform zero-based indices to one-based indices in errors
	 * @param {Error} err
	 * @returns {Error | IndexError} Returns the transformed error
	 */
	function errorTransform$1(err) {
	  if (err && err.isIndexError) {
	    return new _IndexError.IndexError(err.index + 1, err.min + 1, err.max !== undefined ? err.max + 1 : undefined);
	  }
	  return err;
	}
	return errorTransform;
}

var hasRequiredAccess;

function requireAccess () {
	if (hasRequiredAccess) return access;
	hasRequiredAccess = 1;

	Object.defineProperty(access, "__esModule", {
	  value: true
	});
	access.accessFactory = accessFactory;
	var _errorTransform = requireErrorTransform();
	var _customs = requireCustoms();
	function accessFactory(_ref) {
	  let {
	    subset
	  } = _ref;
	  /**
	   * Retrieve part of an object:
	   *
	   * - Retrieve a property from an object
	   * - Retrieve a part of a string
	   * - Retrieve a matrix subset
	   *
	   * @param {Object | Array | Matrix | string} object
	   * @param {Index} index
	   * @return {Object | Array | Matrix | string} Returns the subset
	   */
	  return function access(object, index) {
	    try {
	      if (Array.isArray(object)) {
	        return subset(object, index);
	      } else if (object && typeof object.subset === 'function') {
	        // Matrix
	        return object.subset(index);
	      } else if (typeof object === 'string') {
	        // TODO: move getStringSubset into a separate util file, use that
	        return subset(object, index);
	      } else if (typeof object === 'object') {
	        if (!index.isObjectProperty()) {
	          throw new TypeError('Cannot apply a numeric index as object property');
	        }
	        return (0, _customs.getSafeProperty)(object, index.getObjectProperty());
	      } else {
	        throw new TypeError('Cannot apply index: unsupported type of object');
	      }
	    } catch (err) {
	      throw (0, _errorTransform.errorTransform)(err);
	    }
	  };
	}
	return access;
}

var hasRequiredAccessorNode;

function requireAccessorNode () {
	if (hasRequiredAccessorNode) return AccessorNode;
	hasRequiredAccessorNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(AccessorNode, "__esModule", {
	  value: true
	});
	AccessorNode.createAccessorNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _customs = requireCustoms();
	var _factory = requireFactory$b();
	var _access = requireAccess();
	const name = 'AccessorNode';
	const dependencies = ['subset', 'Node'];
	AccessorNode.createAccessorNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    subset,
	    Node
	  } = _ref;
	  const access = (0, _access.accessFactory)({
	    subset
	  });

	  /**
	   * Are parenthesis needed?
	   * @private
	   */
	  function needParenthesis(node) {
	    // TODO: maybe make a method on the nodes which tells whether they need parenthesis?
	    return !((0, _is.isAccessorNode)(node) || (0, _is.isArrayNode)(node) || (0, _is.isConstantNode)(node) || (0, _is.isFunctionNode)(node) || (0, _is.isObjectNode)(node) || (0, _is.isParenthesisNode)(node) || (0, _is.isSymbolNode)(node));
	  }
	  class AccessorNode extends Node {
	    /**
	     * @constructor AccessorNode
	     * @extends {Node}
	     * Access an object property or get a matrix subset
	     *
	     * @param {Node} object                 The object from which to retrieve
	     *                                      a property or subset.
	     * @param {IndexNode} index             IndexNode containing ranges
	     * @param {boolean} [optionalChaining=false]
	     *     Optional property, if the accessor was written as optional-chaining
	     *     using `a?.b`, or `a?.["b"] with bracket notation.
	     *     Forces evaluate to undefined if the given object is undefined or null.
	     */
	    constructor(object, index) {
	      let optionalChaining = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
	      super();
	      if (!(0, _is.isNode)(object)) {
	        throw new TypeError('Node expected for parameter "object"');
	      }
	      if (!(0, _is.isIndexNode)(index)) {
	        throw new TypeError('IndexNode expected for parameter "index"');
	      }
	      this.object = object;
	      this.index = index;
	      this.optionalChaining = optionalChaining;
	    }

	    // readonly property name
	    get name() {
	      if (this.index) {
	        return this.index.isObjectProperty() ? this.index.getObjectProperty() : '';
	      } else {
	        return this.object.name || '';
	      }
	    }
	    get type() {
	      return name;
	    }
	    get isAccessorNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const evalObject = this.object._compile(math, argNames);
	      const evalIndex = this.index._compile(math, argNames);
	      const optionalChaining = this.optionalChaining;
	      const prevOptionalChaining = (0, _is.isAccessorNode)(this.object) && this.object.optionalChaining;
	      if (this.index.isObjectProperty()) {
	        const prop = this.index.getObjectProperty();
	        return function evalAccessorNode(scope, args, context) {
	          const ctx = context || {};
	          const object = evalObject(scope, args, ctx);
	          if (optionalChaining && object == null) {
	            ctx.optionalShortCircuit = true;
	            return undefined;
	          }
	          if (prevOptionalChaining && ctx !== null && ctx !== void 0 && ctx.optionalShortCircuit) {
	            return undefined;
	          }

	          // get a property from an object evaluated using the scope.
	          return (0, _customs.getSafeProperty)(object, prop);
	        };
	      } else {
	        return function evalAccessorNode(scope, args, context) {
	          const ctx = context || {};
	          const object = evalObject(scope, args, ctx);
	          if (optionalChaining && object == null) {
	            ctx.optionalShortCircuit = true;
	            return undefined;
	          }
	          if (prevOptionalChaining && ctx !== null && ctx !== void 0 && ctx.optionalShortCircuit) {
	            return undefined;
	          }

	          // we pass just object here instead of context:
	          const index = evalIndex(scope, args, object);
	          return access(object, index);
	        };
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.object, 'object', this);
	      callback(this.index, 'index', this);
	    }

	    /**
	     * Create a new AccessorNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {AccessorNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      return new AccessorNode(this._ifNode(callback(this.object, 'object', this)), this._ifNode(callback(this.index, 'index', this)), this.optionalChaining);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {AccessorNode}
	     */
	    clone() {
	      return new AccessorNode(this.object, this.index, this.optionalChaining);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string}
	     */
	    _toString(options) {
	      let object = this.object.toString(options);
	      if (needParenthesis(this.object)) {
	        object = '(' + object + ')';
	      }
	      const optionalChaining = this.optionalChaining ? this.index.dotNotation ? '?' : '?.' : '';
	      return object + optionalChaining + this.index.toString(options);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string}
	     */
	    _toHTML(options) {
	      let object = this.object.toHTML(options);
	      if (needParenthesis(this.object)) {
	        object = '<span class="math-parenthesis math-round-parenthesis">(</span>' + object + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      return object + this.index.toHTML(options);
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string}
	     */
	    _toTex(options) {
	      let object = this.object.toTex(options);
	      if (needParenthesis(this.object)) {
	        object = '\\left(\' + object + \'\\right)';
	      }
	      return object + this.index.toTex(options);
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        object: this.object,
	        index: this.index,
	        optionalChaining: this.optionalChaining
	      };
	    }

	    /**
	     * Instantiate an AccessorNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     `{"mathjs": "AccessorNode", object: ..., index: ...}`,
	     *     where mathjs is optional
	     * @returns {AccessorNode}
	     */
	    static fromJSON(json) {
	      return new AccessorNode(json.object, json.index, json.optionalChaining);
	    }
	  }
	  (0, _defineProperty2.default)(AccessorNode, "name", name);
	  return AccessorNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return AccessorNode;
}

var ArrayNode = {};

var hasRequiredArrayNode;

function requireArrayNode () {
	if (hasRequiredArrayNode) return ArrayNode;
	hasRequiredArrayNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(ArrayNode, "__esModule", {
	  value: true
	});
	ArrayNode.createArrayNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'ArrayNode';
	const dependencies = ['Node'];
	ArrayNode.createArrayNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  class ArrayNode extends Node {
	    /**
	     * @constructor ArrayNode
	     * @extends {Node}
	     * Holds an 1-dimensional array with items
	     * @param {Node[]} [items]   1 dimensional array with items
	     */
	    constructor(items) {
	      super();
	      this.items = items || [];

	      // validate input
	      if (!Array.isArray(this.items) || !this.items.every(_is.isNode)) {
	        throw new TypeError('Array containing Nodes expected');
	      }
	    }
	    get type() {
	      return name;
	    }
	    get isArrayNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const evalItems = (0, _array.map)(this.items, function (item) {
	        return item._compile(math, argNames);
	      });
	      const asMatrix = math.config.matrix !== 'Array';
	      if (asMatrix) {
	        const matrix = math.matrix;
	        return function evalArrayNode(scope, args, context) {
	          return matrix((0, _array.map)(evalItems, function (evalItem) {
	            return evalItem(scope, args, context);
	          }));
	        };
	      } else {
	        return function evalArrayNode(scope, args, context) {
	          return (0, _array.map)(evalItems, function (evalItem) {
	            return evalItem(scope, args, context);
	          });
	        };
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      for (let i = 0; i < this.items.length; i++) {
	        const node = this.items[i];
	        callback(node, 'items[' + i + ']', this);
	      }
	    }

	    /**
	     * Create a new ArrayNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {ArrayNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const items = [];
	      for (let i = 0; i < this.items.length; i++) {
	        items[i] = this._ifNode(callback(this.items[i], 'items[' + i + ']', this));
	      }
	      return new ArrayNode(items);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {ArrayNode}
	     */
	    clone() {
	      return new ArrayNode(this.items.slice(0));
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toString(options) {
	      const items = this.items.map(function (node) {
	        return node.toString(options);
	      });
	      return '[' + items.join(', ') + ']';
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        items: this.items
	      };
	    }

	    /**
	     * Instantiate an ArrayNode from its JSON representation
	     * @param {Object} json  An object structured like
	     *                       `{"mathjs": "ArrayNode", items: [...]}`,
	     *                       where mathjs is optional
	     * @returns {ArrayNode}
	     */
	    static fromJSON(json) {
	      return new ArrayNode(json.items);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toHTML(options) {
	      const items = this.items.map(function (node) {
	        return node.toHTML(options);
	      });
	      return '<span class="math-parenthesis math-square-parenthesis">[</span>' + items.join('<span class="math-separator">,</span>') + '<span class="math-parenthesis math-square-parenthesis">]</span>';
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      function itemsToTex(items, nested) {
	        const mixedItems = items.some(_is.isArrayNode) && !items.every(_is.isArrayNode);
	        const itemsFormRow = nested || mixedItems;
	        const itemSep = itemsFormRow ? '&' : '\\\\';
	        const itemsTex = items.map(function (node) {
	          if (node.items) {
	            return itemsToTex(node.items, !nested);
	          } else {
	            return node.toTex(options);
	          }
	        }).join(itemSep);
	        return mixedItems || !itemsFormRow || itemsFormRow && !nested ? '\\begin{bmatrix}' + itemsTex + '\\end{bmatrix}' : itemsTex;
	      }
	      return itemsToTex(this.items, false);
	    }
	  }
	  (0, _defineProperty2.default)(ArrayNode, "name", name);
	  return ArrayNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return ArrayNode;
}

var AssignmentNode = {};

var assign$2 = {};

var hasRequiredAssign$2;

function requireAssign$2 () {
	if (hasRequiredAssign$2) return assign$2;
	hasRequiredAssign$2 = 1;

	Object.defineProperty(assign$2, "__esModule", {
	  value: true
	});
	assign$2.assignFactory = assignFactory;
	var _errorTransform = requireErrorTransform();
	var _customs = requireCustoms();
	function assignFactory(_ref) {
	  let {
	    subset,
	    matrix
	  } = _ref;
	  /**
	   * Replace part of an object:
	   *
	   * - Assign a property to an object
	   * - Replace a part of a string
	   * - Replace a matrix subset
	   *
	   * @param {Object | Array | Matrix | string} object
	   * @param {Index} index
	   * @param {*} value
	   * @return {Object | Array | Matrix | string} Returns the original object
	   *                                            except in case of a string
	   */
	  // TODO: change assign to return the value instead of the object
	  return function assign(object, index, value) {
	    try {
	      if (Array.isArray(object)) {
	        const result = matrix(object).subset(index, value).valueOf();

	        // shallow copy all (updated) items into the original array
	        result.forEach((item, index) => {
	          object[index] = item;
	        });
	        return object;
	      } else if (object && typeof object.subset === 'function') {
	        // Matrix
	        return object.subset(index, value);
	      } else if (typeof object === 'string') {
	        // TODO: move setStringSubset into a separate util file, use that
	        return subset(object, index, value);
	      } else if (typeof object === 'object') {
	        if (!index.isObjectProperty()) {
	          throw TypeError('Cannot apply a numeric index as object property');
	        }
	        (0, _customs.setSafeProperty)(object, index.getObjectProperty(), value);
	        return object;
	      } else {
	        throw new TypeError('Cannot apply index: unsupported type of object');
	      }
	    } catch (err) {
	      throw (0, _errorTransform.errorTransform)(err);
	    }
	  };
	}
	return assign$2;
}

var operators = {};

var hasRequiredOperators;

function requireOperators () {
	if (hasRequiredOperators) return operators;
	hasRequiredOperators = 1;

	Object.defineProperty(operators, "__esModule", {
	  value: true
	});
	operators.getAssociativity = getAssociativity;
	operators.getOperator = getOperator;
	operators.getPrecedence = getPrecedence;
	operators.isAssociativeWith = isAssociativeWith;
	operators.properties = void 0;
	var _object = requireObject$3();
	var _is = requireIs();
	// list of identifiers of nodes in order of their precedence
	// also contains information about left/right associativity
	// and which other operator the operator is associative with
	// Example:
	// addition is associative with addition and subtraction, because:
	// (a+b)+c=a+(b+c)
	// (a+b)-c=a+(b-c)
	//
	// postfix operators are left associative, prefix operators
	// are right associative
	//
	// It's also possible to set the following properties:
	// latexParens: if set to false, this node doesn't need to be enclosed
	//              in parentheses when using LaTeX
	// latexLeftParens: if set to false, this !OperatorNode's!
	//                  left argument doesn't need to be enclosed
	//                  in parentheses
	// latexRightParens: the same for the right argument

	const properties = operators.properties = [{
	  // assignment
	  AssignmentNode: {},
	  FunctionAssignmentNode: {}
	}, {
	  // conditional expression
	  ConditionalNode: {
	    latexLeftParens: false,
	    latexRightParens: false,
	    latexParens: false
	    // conditionals don't need parentheses in LaTeX because
	    // they are 2 dimensional
	  }
	}, {
	  // logical or
	  'OperatorNode:or': {
	    op: 'or',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // logical xor
	  'OperatorNode:xor': {
	    op: 'xor',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // logical and
	  'OperatorNode:and': {
	    op: 'and',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // bitwise or
	  'OperatorNode:bitOr': {
	    op: '|',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // bitwise xor
	  'OperatorNode:bitXor': {
	    op: '^|',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // bitwise and
	  'OperatorNode:bitAnd': {
	    op: '&',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // relational operators
	  'OperatorNode:equal': {
	    op: '==',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:unequal': {
	    op: '!=',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:smaller': {
	    op: '<',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:larger': {
	    op: '>',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:smallerEq': {
	    op: '<=',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:largerEq': {
	    op: '>=',
	    associativity: 'left',
	    associativeWith: []
	  },
	  RelationalNode: {
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // bitshift operators
	  'OperatorNode:leftShift': {
	    op: '<<',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:rightArithShift': {
	    op: '>>',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:rightLogShift': {
	    op: '>>>',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // unit conversion
	  'OperatorNode:to': {
	    op: 'to',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // range
	  RangeNode: {}
	}, {
	  // addition, subtraction
	  'OperatorNode:add': {
	    op: '+',
	    associativity: 'left',
	    associativeWith: ['OperatorNode:add', 'OperatorNode:subtract']
	  },
	  'OperatorNode:subtract': {
	    op: '-',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // multiply, divide, modulus
	  'OperatorNode:multiply': {
	    op: '*',
	    associativity: 'left',
	    associativeWith: ['OperatorNode:multiply', 'OperatorNode:divide', 'Operator:dotMultiply', 'Operator:dotDivide']
	  },
	  'OperatorNode:divide': {
	    op: '/',
	    associativity: 'left',
	    associativeWith: [],
	    latexLeftParens: false,
	    latexRightParens: false,
	    latexParens: false
	    // fractions don't require parentheses because
	    // they're 2 dimensional, so parens aren't needed
	    // in LaTeX
	  },
	  'OperatorNode:dotMultiply': {
	    op: '.*',
	    associativity: 'left',
	    associativeWith: ['OperatorNode:multiply', 'OperatorNode:divide', 'OperatorNode:dotMultiply', 'OperatorNode:doDivide']
	  },
	  'OperatorNode:dotDivide': {
	    op: './',
	    associativity: 'left',
	    associativeWith: []
	  },
	  'OperatorNode:mod': {
	    op: 'mod',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // Repeat multiplication for implicit multiplication
	  'OperatorNode:multiply': {
	    associativity: 'left',
	    associativeWith: ['OperatorNode:multiply', 'OperatorNode:divide', 'Operator:dotMultiply', 'Operator:dotDivide']
	  }
	}, {
	  // unary prefix operators
	  'OperatorNode:unaryPlus': {
	    op: '+',
	    associativity: 'right'
	  },
	  'OperatorNode:unaryMinus': {
	    op: '-',
	    associativity: 'right'
	  },
	  'OperatorNode:bitNot': {
	    op: '~',
	    associativity: 'right'
	  },
	  'OperatorNode:not': {
	    op: 'not',
	    associativity: 'right'
	  }
	}, {
	  // exponentiation
	  'OperatorNode:pow': {
	    op: '^',
	    associativity: 'right',
	    associativeWith: [],
	    latexRightParens: false
	    // the exponent doesn't need parentheses in
	    // LaTeX because it's 2 dimensional
	    // (it's on top)
	  },
	  'OperatorNode:dotPow': {
	    op: '.^',
	    associativity: 'right',
	    associativeWith: []
	  }
	}, {
	  // nullish coalescing
	  'OperatorNode:nullish': {
	    op: '??',
	    associativity: 'left',
	    associativeWith: []
	  }
	}, {
	  // factorial
	  'OperatorNode:factorial': {
	    op: '!',
	    associativity: 'left'
	  }
	}, {
	  // matrix transpose
	  'OperatorNode:ctranspose': {
	    op: "'",
	    associativity: 'left'
	  }
	}];

	/**
	 * Returns the first non-parenthesis internal node, but only
	 * when the 'parenthesis' option is unset or auto.
	 * @param {Node} _node
	 * @param {string} parenthesis
	 * @return {Node}
	 */
	function unwrapParen(_node, parenthesis) {
	  if (!parenthesis || parenthesis !== 'auto') return _node;
	  let node = _node;
	  while ((0, _is.isParenthesisNode)(node)) node = node.content;
	  return node;
	}

	/**
	 * Get the precedence of a Node.
	 * Higher number for higher precedence, starting with 0.
	 * Returns null if the precedence is undefined.
	 *
	 * @param {Node} _node
	 * @param {string} parenthesis
	 * @param {string} implicit
	 * @param {Node} parent (for determining context for implicit multiplication)
	 * @return {number | null}
	 */
	function getPrecedence(_node, parenthesis, implicit, parent) {
	  let node = _node;
	  if (parenthesis !== 'keep') {
	    // ParenthesisNodes are only ignored when not in 'keep' mode
	    node = _node.getContent();
	  }
	  const identifier = node.getIdentifier();
	  let precedence = null;
	  for (let i = 0; i < properties.length; i++) {
	    if (identifier in properties[i]) {
	      precedence = i;
	      break;
	    }
	  }
	  // Bump up precedence of implicit multiplication, except when preceded
	  // by a "Rule 2" fraction ( [unaryOp]constant / constant )
	  if (identifier === 'OperatorNode:multiply' && node.implicit && implicit !== 'show') {
	    const leftArg = unwrapParen(node.args[0], parenthesis);
	    if (!((0, _is.isConstantNode)(leftArg) && parent && parent.getIdentifier() === 'OperatorNode:divide' && (0, _is.rule2Node)(unwrapParen(parent.args[0], parenthesis))) && !(leftArg.getIdentifier() === 'OperatorNode:divide' && (0, _is.rule2Node)(unwrapParen(leftArg.args[0], parenthesis)) && (0, _is.isConstantNode)(unwrapParen(leftArg.args[1])))) {
	      precedence += 1;
	    }
	  }
	  return precedence;
	}

	/**
	 * Get the associativity of an operator (left or right).
	 * Returns a string containing 'left' or 'right' or null if
	 * the associativity is not defined.
	 *
	 * @param {Node} _node
	 * @param {string} parenthesis
	 * @return {string|null}
	 * @throws {Error}
	 */
	function getAssociativity(_node, parenthesis) {
	  let node = _node;
	  if (parenthesis !== 'keep') {
	    // ParenthesisNodes are only ignored when not in 'keep' mode
	    node = _node.getContent();
	  }
	  const identifier = node.getIdentifier();
	  const index = getPrecedence(node, parenthesis);
	  if (index === null) {
	    // node isn't in the list
	    return null;
	  }
	  const property = properties[index][identifier];
	  if ((0, _object.hasOwnProperty)(property, 'associativity')) {
	    if (property.associativity === 'left') {
	      return 'left';
	    }
	    if (property.associativity === 'right') {
	      return 'right';
	    }
	    // associativity is invalid
	    throw Error('\'' + identifier + '\' has the invalid associativity \'' + property.associativity + '\'.');
	  }

	  // associativity is undefined
	  return null;
	}

	/**
	 * Check if an operator is associative with another operator.
	 * Returns either true or false or null if not defined.
	 *
	 * @param {Node} nodeA
	 * @param {Node} nodeB
	 * @param {string} parenthesis
	 * @return {boolean | null}
	 */
	function isAssociativeWith(nodeA, nodeB, parenthesis) {
	  // ParenthesisNodes are only ignored when not in 'keep' mode
	  const a = parenthesis !== 'keep' ? nodeA.getContent() : nodeA;
	  const b = parenthesis !== 'keep' ? nodeA.getContent() : nodeB;
	  const identifierA = a.getIdentifier();
	  const identifierB = b.getIdentifier();
	  const index = getPrecedence(a, parenthesis);
	  if (index === null) {
	    // node isn't in the list
	    return null;
	  }
	  const property = properties[index][identifierA];
	  if ((0, _object.hasOwnProperty)(property, 'associativeWith') && property.associativeWith instanceof Array) {
	    for (let i = 0; i < property.associativeWith.length; i++) {
	      if (property.associativeWith[i] === identifierB) {
	        return true;
	      }
	    }
	    return false;
	  }

	  // associativeWith is not defined
	  return null;
	}

	/**
	 * Get the operator associated with a function name.
	 * Returns a string with the operator symbol, or null if the
	 * input is not the name of a function associated with an
	 * operator.
	 *
	 * @param {string} Function name
	 * @return {string | null} Associated operator symbol, if any
	 */
	function getOperator(fn) {
	  const identifier = 'OperatorNode:' + fn;
	  for (const group of properties) {
	    if (identifier in group) {
	      return group[identifier].op;
	    }
	  }
	  return null;
	}
	return operators;
}

var hasRequiredAssignmentNode;

function requireAssignmentNode () {
	if (hasRequiredAssignmentNode) return AssignmentNode;
	hasRequiredAssignmentNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(AssignmentNode, "__esModule", {
	  value: true
	});
	AssignmentNode.createAssignmentNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _customs = requireCustoms();
	var _factory = requireFactory$b();
	var _access = requireAccess();
	var _assign = requireAssign$2();
	var _operators = requireOperators();
	const name = 'AssignmentNode';
	const dependencies = ['subset', '?matrix',
	// FIXME: should not be needed at all, should be handled by subset
	'Node'];
	AssignmentNode.createAssignmentNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    subset,
	    matrix,
	    Node
	  } = _ref;
	  const access = (0, _access.accessFactory)({
	    subset
	  });
	  const assign = (0, _assign.assignFactory)({
	    subset,
	    matrix
	  });

	  /*
	   * Is parenthesis needed?
	   * @param {node} node
	   * @param {string} [parenthesis='keep']
	   * @param {string} implicit
	   * @private
	   */
	  function needParenthesis(node, parenthesis, implicit) {
	    if (!parenthesis) {
	      parenthesis = 'keep';
	    }
	    const precedence = (0, _operators.getPrecedence)(node, parenthesis, implicit);
	    const exprPrecedence = (0, _operators.getPrecedence)(node.value, parenthesis, implicit);
	    return parenthesis === 'all' || exprPrecedence !== null && exprPrecedence <= precedence;
	  }
	  class AssignmentNode extends Node {
	    /**
	     * @constructor AssignmentNode
	     * @extends {Node}
	     *
	     * Define a symbol, like `a=3.2`, update a property like `a.b=3.2`, or
	     * replace a subset of a matrix like `A[2,2]=42`.
	     *
	     * Syntax:
	     *
	     *     new AssignmentNode(symbol, value)
	     *     new AssignmentNode(object, index, value)
	     *
	     * Usage:
	     *
	     *    new AssignmentNode(new SymbolNode('a'), new ConstantNode(2))  // a=2
	     *    new AssignmentNode(new SymbolNode('a'),
	     *                       new IndexNode('b'),
	     *                       new ConstantNode(2))   // a.b=2
	     *    new AssignmentNode(new SymbolNode('a'),
	     *                       new IndexNode(1, 2),
	     *                       new ConstantNode(3))  // a[1,2]=3
	     *
	     * @param {SymbolNode | AccessorNode} object
	     *     Object on which to assign a value
	     * @param {IndexNode} [index=null]
	     *     Index, property name or matrix index. Optional. If not provided
	     *     and `object` is a SymbolNode, the property is assigned to the
	     *     global scope.
	     * @param {Node} value
	     *     The value to be assigned
	     */
	    constructor(object, index, value) {
	      super();
	      this.object = object;
	      this.index = value ? index : null;
	      this.value = value || index;

	      // validate input
	      if (!(0, _is.isSymbolNode)(object) && !(0, _is.isAccessorNode)(object)) {
	        throw new TypeError('SymbolNode or AccessorNode expected as "object"');
	      }
	      if ((0, _is.isSymbolNode)(object) && object.name === 'end') {
	        throw new Error('Cannot assign to symbol "end"');
	      }
	      if (this.index && !(0, _is.isIndexNode)(this.index)) {
	        // index is optional
	        throw new TypeError('IndexNode expected as "index"');
	      }
	      if (!(0, _is.isNode)(this.value)) {
	        throw new TypeError('Node expected as "value"');
	      }
	    }

	    // class name for typing purposes:

	    // readonly property name
	    get name() {
	      if (this.index) {
	        return this.index.isObjectProperty() ? this.index.getObjectProperty() : '';
	      } else {
	        return this.object.name || '';
	      }
	    }
	    get type() {
	      return name;
	    }
	    get isAssignmentNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const evalObject = this.object._compile(math, argNames);
	      const evalIndex = this.index ? this.index._compile(math, argNames) : null;
	      const evalValue = this.value._compile(math, argNames);
	      const name = this.object.name;
	      if (!this.index) {
	        // apply a variable to the scope, for example `a=2`
	        if (!(0, _is.isSymbolNode)(this.object)) {
	          throw new TypeError('SymbolNode expected as object');
	        }
	        return function evalAssignmentNode(scope, args, context) {
	          const value = evalValue(scope, args, context);
	          scope.set(name, value);
	          return value;
	        };
	      } else if (this.index.isObjectProperty()) {
	        // apply an object property for example `a.b=2`
	        const prop = this.index.getObjectProperty();
	        return function evalAssignmentNode(scope, args, context) {
	          const object = evalObject(scope, args, context);
	          const value = evalValue(scope, args, context);
	          (0, _customs.setSafeProperty)(object, prop, value);
	          return value;
	        };
	      } else if ((0, _is.isSymbolNode)(this.object)) {
	        // update a matrix subset, for example `a[2]=3`
	        return function evalAssignmentNode(scope, args, context) {
	          const childObject = evalObject(scope, args, context);
	          const value = evalValue(scope, args, context);
	          // Important:  we pass childObject instead of context:
	          const index = evalIndex(scope, args, childObject);
	          scope.set(name, assign(childObject, index, value));
	          return value;
	        };
	      } else {
	        // isAccessorNode(node.object) === true
	        // update a matrix subset, for example `a.b[2]=3`

	        // we will not use the compile function of the AccessorNode, but
	        // compile it ourselves here as we need the parent object of the
	        // AccessorNode:
	        // wee need to apply the updated object to parent object
	        const evalParentObject = this.object.object._compile(math, argNames);
	        if (this.object.index.isObjectProperty()) {
	          const parentProp = this.object.index.getObjectProperty();
	          return function evalAssignmentNode(scope, args, context) {
	            const parent = evalParentObject(scope, args, context);
	            const childObject = (0, _customs.getSafeProperty)(parent, parentProp);
	            // Important: we pass childObject instead of context:
	            const index = evalIndex(scope, args, childObject);
	            const value = evalValue(scope, args, context);
	            (0, _customs.setSafeProperty)(parent, parentProp, assign(childObject, index, value));
	            return value;
	          };
	        } else {
	          // if some parameters use the 'end' parameter, we need to calculate
	          // the size
	          const evalParentIndex = this.object.index._compile(math, argNames);
	          return function evalAssignmentNode(scope, args, context) {
	            const parent = evalParentObject(scope, args, context);
	            // Important: we pass parent instead of context:
	            const parentIndex = evalParentIndex(scope, args, parent);
	            const childObject = access(parent, parentIndex);
	            // Important:  we pass childObject instead of context
	            const index = evalIndex(scope, args, childObject);
	            const value = evalValue(scope, args, context);
	            assign(parent, parentIndex, assign(childObject, index, value));
	            return value;
	          };
	        }
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.object, 'object', this);
	      if (this.index) {
	        callback(this.index, 'index', this);
	      }
	      callback(this.value, 'value', this);
	    }

	    /**
	     * Create a new AssignmentNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {AssignmentNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const object = this._ifNode(callback(this.object, 'object', this));
	      const index = this.index ? this._ifNode(callback(this.index, 'index', this)) : null;
	      const value = this._ifNode(callback(this.value, 'value', this));
	      return new AssignmentNode(object, index, value);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {AssignmentNode}
	     */
	    clone() {
	      return new AssignmentNode(this.object, this.index, this.value);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string}
	     */
	    _toString(options) {
	      const object = this.object.toString(options);
	      const index = this.index ? this.index.toString(options) : '';
	      let value = this.value.toString(options);
	      if (needParenthesis(this, options && options.parenthesis, options && options.implicit)) {
	        value = '(' + value + ')';
	      }
	      return object + index + ' = ' + value;
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        object: this.object,
	        index: this.index,
	        value: this.value
	      };
	    }

	    /**
	     * Instantiate an AssignmentNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     `{"mathjs": "AssignmentNode", object: ..., index: ..., value: ...}`,
	     *     where mathjs is optional
	     * @returns {AssignmentNode}
	     */
	    static fromJSON(json) {
	      return new AssignmentNode(json.object, json.index, json.value);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string}
	     */
	    _toHTML(options) {
	      const object = this.object.toHTML(options);
	      const index = this.index ? this.index.toHTML(options) : '';
	      let value = this.value.toHTML(options);
	      if (needParenthesis(this, options && options.parenthesis, options && options.implicit)) {
	        value = '<span class="math-paranthesis math-round-parenthesis">(</span>' + value + '<span class="math-paranthesis math-round-parenthesis">)</span>';
	      }
	      return object + index + '<span class="math-operator math-assignment-operator ' + 'math-variable-assignment-operator math-binary-operator">=</span>' + value;
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string}
	     */
	    _toTex(options) {
	      const object = this.object.toTex(options);
	      const index = this.index ? this.index.toTex(options) : '';
	      let value = this.value.toTex(options);
	      if (needParenthesis(this, options && options.parenthesis, options && options.implicit)) {
	        value = `\\left(${value}\\right)`;
	      }
	      return object + index + '=' + value;
	    }
	  }
	  (0, _defineProperty2.default)(AssignmentNode, "name", name);
	  return AssignmentNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return AssignmentNode;
}

var BlockNode = {};

var hasRequiredBlockNode;

function requireBlockNode () {
	if (hasRequiredBlockNode) return BlockNode;
	hasRequiredBlockNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(BlockNode, "__esModule", {
	  value: true
	});
	BlockNode.createBlockNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'BlockNode';
	const dependencies = ['ResultSet', 'Node'];
	BlockNode.createBlockNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    ResultSet,
	    Node
	  } = _ref;
	  class BlockNode extends Node {
	    /**
	     * @constructor BlockNode
	     * @extends {Node}
	     * Holds a set with blocks
	     * @param {Array.<{node: Node} | {node: Node, visible: boolean}>} blocks
	     *            An array with blocks, where a block is constructed as an
	     *            Object with properties block, which is a Node, and visible,
	     *            which is a boolean. The property visible is optional and
	     *            is true by default
	     */
	    constructor(blocks) {
	      super();
	      // validate input, copy blocks
	      if (!Array.isArray(blocks)) throw new Error('Array expected');
	      this.blocks = blocks.map(function (block) {
	        const node = block && block.node;
	        const visible = block && block.visible !== undefined ? block.visible : true;
	        if (!(0, _is.isNode)(node)) throw new TypeError('Property "node" must be a Node');
	        if (typeof visible !== 'boolean') {
	          throw new TypeError('Property "visible" must be a boolean');
	        }
	        return {
	          node,
	          visible
	        };
	      });
	    }
	    get type() {
	      return name;
	    }
	    get isBlockNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const evalBlocks = (0, _array.map)(this.blocks, function (block) {
	        return {
	          evaluate: block.node._compile(math, argNames),
	          visible: block.visible
	        };
	      });
	      return function evalBlockNodes(scope, args, context) {
	        const results = [];
	        (0, _array.forEach)(evalBlocks, function evalBlockNode(block) {
	          const result = block.evaluate(scope, args, context);
	          if (block.visible) {
	            results.push(result);
	          }
	        });
	        return new ResultSet(results);
	      };
	    }

	    /**
	     * Execute a callback for each of the child blocks of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      for (let i = 0; i < this.blocks.length; i++) {
	        callback(this.blocks[i].node, 'blocks[' + i + '].node', this);
	      }
	    }

	    /**
	     * Create a new BlockNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {BlockNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const blocks = [];
	      for (let i = 0; i < this.blocks.length; i++) {
	        const block = this.blocks[i];
	        const node = this._ifNode(callback(block.node, 'blocks[' + i + '].node', this));
	        blocks[i] = {
	          node,
	          visible: block.visible
	        };
	      }
	      return new BlockNode(blocks);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {BlockNode}
	     */
	    clone() {
	      const blocks = this.blocks.map(function (block) {
	        return {
	          node: block.node,
	          visible: block.visible
	        };
	      });
	      return new BlockNode(blocks);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toString(options) {
	      return this.blocks.map(function (param) {
	        return param.node.toString(options) + (param.visible ? '' : ';');
	      }).join('\n');
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        blocks: this.blocks
	      };
	    }

	    /**
	     * Instantiate an BlockNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     `{"mathjs": "BlockNode", blocks: [{node: ..., visible: false}, ...]}`,
	     *     where mathjs is optional
	     * @returns {BlockNode}
	     */
	    static fromJSON(json) {
	      return new BlockNode(json.blocks);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toHTML(options) {
	      return this.blocks.map(function (param) {
	        return param.node.toHTML(options) + (param.visible ? '' : '<span class="math-separator">;</span>');
	      }).join('<span class="math-separator"><br /></span>');
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      return this.blocks.map(function (param) {
	        return param.node.toTex(options) + (param.visible ? '' : ';');
	      }).join('\\;\\;\n');
	    }
	  }
	  (0, _defineProperty2.default)(BlockNode, "name", name);
	  return BlockNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return BlockNode;
}

var ConditionalNode = {};

var hasRequiredConditionalNode;

function requireConditionalNode () {
	if (hasRequiredConditionalNode) return ConditionalNode;
	hasRequiredConditionalNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(ConditionalNode, "__esModule", {
	  value: true
	});
	ConditionalNode.createConditionalNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _operators = requireOperators();
	const name = 'ConditionalNode';
	const dependencies = ['Node'];
	ConditionalNode.createConditionalNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  /**
	   * Test whether a condition is met
	   * @param {*} condition
	   * @returns {boolean} true if condition is true or non-zero, else false
	   */
	  function testCondition(condition) {
	    if (typeof condition === 'number' || typeof condition === 'boolean' || typeof condition === 'string') {
	      return !!condition;
	    }
	    if (condition) {
	      if ((0, _is.isBigNumber)(condition)) {
	        return !condition.isZero();
	      }
	      if ((0, _is.isComplex)(condition)) {
	        return !!(condition.re || condition.im);
	      }
	      if ((0, _is.isUnit)(condition)) {
	        return !!condition.value;
	      }
	    }
	    if (condition === null || condition === undefined) {
	      return false;
	    }
	    throw new TypeError('Unsupported type of condition "' + (0, _is.typeOf)(condition) + '"');
	  }
	  class ConditionalNode extends Node {
	    /**
	     * A lazy evaluating conditional operator: 'condition ? trueExpr : falseExpr'
	     *
	     * @param {Node} condition   Condition, must result in a boolean
	     * @param {Node} trueExpr    Expression evaluated when condition is true
	     * @param {Node} falseExpr   Expression evaluated when condition is true
	     *
	     * @constructor ConditionalNode
	     * @extends {Node}
	     */
	    constructor(condition, trueExpr, falseExpr) {
	      super();
	      if (!(0, _is.isNode)(condition)) {
	        throw new TypeError('Parameter condition must be a Node');
	      }
	      if (!(0, _is.isNode)(trueExpr)) {
	        throw new TypeError('Parameter trueExpr must be a Node');
	      }
	      if (!(0, _is.isNode)(falseExpr)) {
	        throw new TypeError('Parameter falseExpr must be a Node');
	      }
	      this.condition = condition;
	      this.trueExpr = trueExpr;
	      this.falseExpr = falseExpr;
	    }
	    get type() {
	      return name;
	    }
	    get isConditionalNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const evalCondition = this.condition._compile(math, argNames);
	      const evalTrueExpr = this.trueExpr._compile(math, argNames);
	      const evalFalseExpr = this.falseExpr._compile(math, argNames);
	      return function evalConditionalNode(scope, args, context) {
	        return testCondition(evalCondition(scope, args, context)) ? evalTrueExpr(scope, args, context) : evalFalseExpr(scope, args, context);
	      };
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.condition, 'condition', this);
	      callback(this.trueExpr, 'trueExpr', this);
	      callback(this.falseExpr, 'falseExpr', this);
	    }

	    /**
	     * Create a new ConditionalNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {ConditionalNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      return new ConditionalNode(this._ifNode(callback(this.condition, 'condition', this)), this._ifNode(callback(this.trueExpr, 'trueExpr', this)), this._ifNode(callback(this.falseExpr, 'falseExpr', this)));
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {ConditionalNode}
	     */
	    clone() {
	      return new ConditionalNode(this.condition, this.trueExpr, this.falseExpr);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const precedence = (0, _operators.getPrecedence)(this, parenthesis, options && options.implicit);

	      // Enclose Arguments in parentheses if they are an OperatorNode
	      // or have lower or equal precedence
	      // NOTE: enclosing all OperatorNodes in parentheses is a decision
	      // purely based on aesthetics and readability
	      let condition = this.condition.toString(options);
	      const conditionPrecedence = (0, _operators.getPrecedence)(this.condition, parenthesis, options && options.implicit);
	      if (parenthesis === 'all' || this.condition.type === 'OperatorNode' || conditionPrecedence !== null && conditionPrecedence <= precedence) {
	        condition = '(' + condition + ')';
	      }
	      let trueExpr = this.trueExpr.toString(options);
	      const truePrecedence = (0, _operators.getPrecedence)(this.trueExpr, parenthesis, options && options.implicit);
	      if (parenthesis === 'all' || this.trueExpr.type === 'OperatorNode' || truePrecedence !== null && truePrecedence <= precedence) {
	        trueExpr = '(' + trueExpr + ')';
	      }
	      let falseExpr = this.falseExpr.toString(options);
	      const falsePrecedence = (0, _operators.getPrecedence)(this.falseExpr, parenthesis, options && options.implicit);
	      if (parenthesis === 'all' || this.falseExpr.type === 'OperatorNode' || falsePrecedence !== null && falsePrecedence <= precedence) {
	        falseExpr = '(' + falseExpr + ')';
	      }
	      return condition + ' ? ' + trueExpr + ' : ' + falseExpr;
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        condition: this.condition,
	        trueExpr: this.trueExpr,
	        falseExpr: this.falseExpr
	      };
	    }

	    /**
	     * Instantiate an ConditionalNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     ```
	     *     {"mathjs": "ConditionalNode",
	     *      "condition": ...,
	     *      "trueExpr": ...,
	     *      "falseExpr": ...}
	     *     ```
	     *     where mathjs is optional
	     * @returns {ConditionalNode}
	     */
	    static fromJSON(json) {
	      return new ConditionalNode(json.condition, json.trueExpr, json.falseExpr);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const precedence = (0, _operators.getPrecedence)(this, parenthesis, options && options.implicit);

	      // Enclose Arguments in parentheses if they are an OperatorNode
	      // or have lower or equal precedence
	      // NOTE: enclosing all OperatorNodes in parentheses is a decision
	      // purely based on aesthetics and readability
	      let condition = this.condition.toHTML(options);
	      const conditionPrecedence = (0, _operators.getPrecedence)(this.condition, parenthesis, options && options.implicit);
	      if (parenthesis === 'all' || this.condition.type === 'OperatorNode' || conditionPrecedence !== null && conditionPrecedence <= precedence) {
	        condition = '<span class="math-parenthesis math-round-parenthesis">(</span>' + condition + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      let trueExpr = this.trueExpr.toHTML(options);
	      const truePrecedence = (0, _operators.getPrecedence)(this.trueExpr, parenthesis, options && options.implicit);
	      if (parenthesis === 'all' || this.trueExpr.type === 'OperatorNode' || truePrecedence !== null && truePrecedence <= precedence) {
	        trueExpr = '<span class="math-parenthesis math-round-parenthesis">(</span>' + trueExpr + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      let falseExpr = this.falseExpr.toHTML(options);
	      const falsePrecedence = (0, _operators.getPrecedence)(this.falseExpr, parenthesis, options && options.implicit);
	      if (parenthesis === 'all' || this.falseExpr.type === 'OperatorNode' || falsePrecedence !== null && falsePrecedence <= precedence) {
	        falseExpr = '<span class="math-parenthesis math-round-parenthesis">(</span>' + falseExpr + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      return condition + '<span class="math-operator math-conditional-operator">?</span>' + trueExpr + '<span class="math-operator math-conditional-operator">:</span>' + falseExpr;
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      return '\\begin{cases} {' + this.trueExpr.toTex(options) + '}, &\\quad{\\text{if }\\;' + this.condition.toTex(options) + '}\\\\{' + this.falseExpr.toTex(options) + '}, &\\quad{\\text{otherwise}}\\end{cases}';
	    }
	  }
	  (0, _defineProperty2.default)(ConditionalNode, "name", name);
	  return ConditionalNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return ConditionalNode;
}

var ConstantNode = {};

var latex = {};

var dist;
var hasRequiredDist$2;

function requireDist$2 () {
	if (hasRequiredDist$2) return dist;
	hasRequiredDist$2 = 1;

	// Map the characters to escape to their escaped values. The list is derived
	// from http://www.cespedes.org/blog/85/how-to-escape-latex-special-characters

	var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

	var defaultEscapes = {
	  "{": "\\{",
	  "}": "\\}",
	  "\\": "\\textbackslash{}",
	  "#": "\\#",
	  $: "\\$",
	  "%": "\\%",
	  "&": "\\&",
	  "^": "\\textasciicircum{}",
	  _: "\\_",
	  "~": "\\textasciitilde{}"
	};
	var formatEscapes = {
	  "\u2013": "\\--",
	  "\u2014": "\\---",
	  " ": "~",
	  "\t": "\\qquad{}",
	  "\r\n": "\\newline{}",
	  "\n": "\\newline{}"
	};

	var defaultEscapeMapFn = function defaultEscapeMapFn(defaultEscapes, formatEscapes) {
	  return _extends({}, defaultEscapes, formatEscapes);
	};

	/**
	 * Escape a string to be used in LaTeX documents.
	 * @param {string} str the string to be escaped.
	 * @param {boolean} params.preserveFormatting whether formatting escapes should
	 *  be performed (default: false).
	 * @param {function} params.escapeMapFn the function to modify the escape maps.
	 * @return {string} the escaped string, ready to be used in LaTeX.
	 */
	dist = function (str) {
	  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
	      _ref$preserveFormatti = _ref.preserveFormatting,
	      preserveFormatting = _ref$preserveFormatti === undefined ? false : _ref$preserveFormatti,
	      _ref$escapeMapFn = _ref.escapeMapFn,
	      escapeMapFn = _ref$escapeMapFn === undefined ? defaultEscapeMapFn : _ref$escapeMapFn;

	  var runningStr = String(str);
	  var result = "";

	  var escapes = escapeMapFn(_extends({}, defaultEscapes), preserveFormatting ? _extends({}, formatEscapes) : {});
	  var escapeKeys = Object.keys(escapes); // as it is reused later on

	  // Algorithm: Go through the string character by character, if it matches
	  // with one of the special characters then we'll replace it with the escaped
	  // version.

	  var _loop = function _loop() {
	    var specialCharFound = false;
	    escapeKeys.forEach(function (key, index) {
	      if (specialCharFound) {
	        return;
	      }
	      if (runningStr.length >= key.length && runningStr.slice(0, key.length) === key) {
	        result += escapes[escapeKeys[index]];
	        runningStr = runningStr.slice(key.length, runningStr.length);
	        specialCharFound = true;
	      }
	    });
	    if (!specialCharFound) {
	      result += runningStr.slice(0, 1);
	      runningStr = runningStr.slice(1, runningStr.length);
	    }
	  };

	  while (runningStr) {
	    _loop();
	  }
	  return result;
	};
	return dist;
}

var hasRequiredLatex;

function requireLatex () {
	if (hasRequiredLatex) return latex;
	hasRequiredLatex = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(latex, "__esModule", {
	  value: true
	});
	latex.defaultTemplate = void 0;
	latex.escapeLatex = escapeLatex;
	latex.latexSymbols = latex.latexOperators = latex.latexFunctions = void 0;
	latex.toSymbol = toSymbol;
	var _escapeLatex = _interopRequireDefault(requireDist$2());
	var _object = requireObject$3();
	/* eslint no-template-curly-in-string: "off" */

	const latexSymbols = latex.latexSymbols = {
	  // GREEK LETTERS
	  Alpha: 'A',
	  alpha: '\\alpha',
	  Beta: 'B',
	  beta: '\\beta',
	  Gamma: '\\Gamma',
	  gamma: '\\gamma',
	  Delta: '\\Delta',
	  delta: '\\delta',
	  Epsilon: 'E',
	  epsilon: '\\epsilon',
	  varepsilon: '\\varepsilon',
	  Zeta: 'Z',
	  zeta: '\\zeta',
	  Eta: 'H',
	  eta: '\\eta',
	  Theta: '\\Theta',
	  theta: '\\theta',
	  vartheta: '\\vartheta',
	  Iota: 'I',
	  iota: '\\iota',
	  Kappa: 'K',
	  kappa: '\\kappa',
	  varkappa: '\\varkappa',
	  Lambda: '\\Lambda',
	  lambda: '\\lambda',
	  Mu: 'M',
	  mu: '\\mu',
	  Nu: 'N',
	  nu: '\\nu',
	  Xi: '\\Xi',
	  xi: '\\xi',
	  Omicron: 'O',
	  omicron: 'o',
	  Pi: '\\Pi',
	  pi: '\\pi',
	  varpi: '\\varpi',
	  Rho: 'P',
	  rho: '\\rho',
	  varrho: '\\varrho',
	  Sigma: '\\Sigma',
	  sigma: '\\sigma',
	  varsigma: '\\varsigma',
	  Tau: 'T',
	  tau: '\\tau',
	  Upsilon: '\\Upsilon',
	  upsilon: '\\upsilon',
	  Phi: '\\Phi',
	  phi: '\\phi',
	  varphi: '\\varphi',
	  Chi: 'X',
	  chi: '\\chi',
	  Psi: '\\Psi',
	  psi: '\\psi',
	  Omega: '\\Omega',
	  omega: '\\omega',
	  // logic
	  true: '\\mathrm{True}',
	  false: '\\mathrm{False}',
	  // other
	  i: 'i',
	  // TODO use \i ??
	  inf: '\\infty',
	  Inf: '\\infty',
	  infinity: '\\infty',
	  Infinity: '\\infty',
	  oo: '\\infty',
	  lim: '\\lim',
	  undefined: '\\mathbf{?}'
	};
	const latexOperators = latex.latexOperators = {
	  transpose: '^\\top',
	  ctranspose: '^H',
	  factorial: '!',
	  pow: '^',
	  dotPow: '.^\\wedge',
	  // TODO find ideal solution
	  unaryPlus: '+',
	  unaryMinus: '-',
	  bitNot: '\\~',
	  // TODO find ideal solution
	  not: '\\neg',
	  multiply: '\\cdot',
	  divide: '\\frac',
	  // TODO how to handle that properly?
	  dotMultiply: '.\\cdot',
	  // TODO find ideal solution
	  dotDivide: '.:',
	  // TODO find ideal solution
	  mod: '\\mod',
	  add: '+',
	  subtract: '-',
	  to: '\\rightarrow',
	  leftShift: '<<',
	  rightArithShift: '>>',
	  rightLogShift: '>>>',
	  equal: '=',
	  unequal: '\\neq',
	  smaller: '<',
	  larger: '>',
	  smallerEq: '\\leq',
	  largerEq: '\\geq',
	  bitAnd: '\\&',
	  bitXor: '\\underline{|}',
	  bitOr: '|',
	  and: '\\wedge',
	  xor: '\\veebar',
	  or: '\\vee'
	};
	latex.latexFunctions = {
	  // arithmetic
	  abs: {
	    1: '\\left|${args[0]}\\right|'
	  },
	  add: {
	    2: `\\left(\${args[0]}${latexOperators.add}\${args[1]}\\right)`
	  },
	  cbrt: {
	    1: '\\sqrt[3]{${args[0]}}'
	  },
	  ceil: {
	    1: '\\left\\lceil${args[0]}\\right\\rceil'
	  },
	  cube: {
	    1: '\\left(${args[0]}\\right)^3'
	  },
	  divide: {
	    2: '\\frac{${args[0]}}{${args[1]}}'
	  },
	  dotDivide: {
	    2: `\\left(\${args[0]}${latexOperators.dotDivide}\${args[1]}\\right)`
	  },
	  dotMultiply: {
	    2: `\\left(\${args[0]}${latexOperators.dotMultiply}\${args[1]}\\right)`
	  },
	  dotPow: {
	    2: `\\left(\${args[0]}${latexOperators.dotPow}\${args[1]}\\right)`
	  },
	  exp: {
	    1: '\\exp\\left(${args[0]}\\right)'
	  },
	  expm1: `\\left(e${latexOperators.pow}{\${args[0]}}-1\\right)`,
	  fix: {
	    1: '\\mathrm{${name}}\\left(${args[0]}\\right)'
	  },
	  floor: {
	    1: '\\left\\lfloor${args[0]}\\right\\rfloor'
	  },
	  fraction: {
	    2: '\\frac{${args[0]}}{${args[1]}}'
	  },
	  gcd: '\\gcd\\left(${args}\\right)',
	  hypot: '\\hypot\\left(${args}\\right)',
	  log: {
	    1: '\\ln\\left(${args[0]}\\right)',
	    2: '\\log_{${args[1]}}\\left(${args[0]}\\right)'
	  },
	  log10: {
	    1: '\\log_{10}\\left(${args[0]}\\right)'
	  },
	  log1p: {
	    1: '\\ln\\left(${args[0]}+1\\right)',
	    2: '\\log_{${args[1]}}\\left(${args[0]}+1\\right)'
	  },
	  log2: '\\log_{2}\\left(${args[0]}\\right)',
	  mod: {
	    2: `\\left(\${args[0]}${latexOperators.mod}\${args[1]}\\right)`
	  },
	  multiply: {
	    2: `\\left(\${args[0]}${latexOperators.multiply}\${args[1]}\\right)`
	  },
	  norm: {
	    1: '\\left\\|${args[0]}\\right\\|',
	    2: undefined // use default template
	  },
	  nthRoot: {
	    2: '\\sqrt[${args[1]}]{${args[0]}}'
	  },
	  nthRoots: {
	    2: '\\{y : y^${args[1]} = {${args[0]}}\\}'
	  },
	  pow: {
	    2: `\\left(\${args[0]}\\right)${latexOperators.pow}{\${args[1]}}`
	  },
	  round: {
	    1: '\\left\\lfloor${args[0]}\\right\\rceil',
	    2: undefined // use default template
	  },
	  sign: {
	    1: '\\mathrm{${name}}\\left(${args[0]}\\right)'
	  },
	  sqrt: {
	    1: '\\sqrt{${args[0]}}'
	  },
	  square: {
	    1: '\\left(${args[0]}\\right)^2'
	  },
	  subtract: {
	    2: `\\left(\${args[0]}${latexOperators.subtract}\${args[1]}\\right)`
	  },
	  unaryMinus: {
	    1: `${latexOperators.unaryMinus}\\left(\${args[0]}\\right)`
	  },
	  unaryPlus: {
	    1: `${latexOperators.unaryPlus}\\left(\${args[0]}\\right)`
	  },
	  // bitwise
	  bitAnd: {
	    2: `\\left(\${args[0]}${latexOperators.bitAnd}\${args[1]}\\right)`
	  },
	  bitNot: {
	    1: latexOperators.bitNot + '\\left(${args[0]}\\right)'
	  },
	  bitOr: {
	    2: `\\left(\${args[0]}${latexOperators.bitOr}\${args[1]}\\right)`
	  },
	  bitXor: {
	    2: `\\left(\${args[0]}${latexOperators.bitXor}\${args[1]}\\right)`
	  },
	  leftShift: {
	    2: `\\left(\${args[0]}${latexOperators.leftShift}\${args[1]}\\right)`
	  },
	  rightArithShift: {
	    2: `\\left(\${args[0]}${latexOperators.rightArithShift}\${args[1]}\\right)`
	  },
	  rightLogShift: {
	    2: `\\left(\${args[0]}${latexOperators.rightLogShift}\${args[1]}\\right)`
	  },
	  // combinatorics
	  bellNumbers: {
	    1: '\\mathrm{B}_{${args[0]}}'
	  },
	  catalan: {
	    1: '\\mathrm{C}_{${args[0]}}'
	  },
	  stirlingS2: {
	    2: '\\mathrm{S}\\left(${args}\\right)'
	  },
	  // complex
	  arg: {
	    1: '\\arg\\left(${args[0]}\\right)'
	  },
	  conj: {
	    1: '\\left(${args[0]}\\right)^*'
	  },
	  im: {
	    1: '\\Im\\left\\lbrace${args[0]}\\right\\rbrace'
	  },
	  re: {
	    1: '\\Re\\left\\lbrace${args[0]}\\right\\rbrace'
	  },
	  // logical
	  and: {
	    2: `\\left(\${args[0]}${latexOperators.and}\${args[1]}\\right)`
	  },
	  not: {
	    1: latexOperators.not + '\\left(${args[0]}\\right)'
	  },
	  or: {
	    2: `\\left(\${args[0]}${latexOperators.or}\${args[1]}\\right)`
	  },
	  xor: {
	    2: `\\left(\${args[0]}${latexOperators.xor}\${args[1]}\\right)`
	  },
	  // matrix
	  cross: {
	    2: '\\left(${args[0]}\\right)\\times\\left(${args[1]}\\right)'
	  },
	  ctranspose: {
	    1: `\\left(\${args[0]}\\right)${latexOperators.ctranspose}`
	  },
	  det: {
	    1: '\\det\\left(${args[0]}\\right)'
	  },
	  dot: {
	    2: '\\left(${args[0]}\\cdot${args[1]}\\right)'
	  },
	  expm: {
	    1: '\\exp\\left(${args[0]}\\right)'
	  },
	  inv: {
	    1: '\\left(${args[0]}\\right)^{-1}'
	  },
	  pinv: {
	    1: '\\left(${args[0]}\\right)^{+}'
	  },
	  sqrtm: {
	    1: `{\${args[0]}}${latexOperators.pow}{\\frac{1}{2}}`
	  },
	  trace: {
	    1: '\\mathrm{tr}\\left(${args[0]}\\right)'
	  },
	  transpose: {
	    1: `\\left(\${args[0]}\\right)${latexOperators.transpose}`
	  },
	  // probability
	  combinations: {
	    2: '\\binom{${args[0]}}{${args[1]}}'
	  },
	  combinationsWithRep: {
	    2: '\\left(\\!\\!{\\binom{${args[0]}}{${args[1]}}}\\!\\!\\right)'
	  },
	  factorial: {
	    1: `\\left(\${args[0]}\\right)${latexOperators.factorial}`
	  },
	  gamma: {
	    1: '\\Gamma\\left(${args[0]}\\right)'
	  },
	  lgamma: {
	    1: '\\ln\\Gamma\\left(${args[0]}\\right)'
	  },
	  // relational
	  equal: {
	    2: `\\left(\${args[0]}${latexOperators.equal}\${args[1]}\\right)`
	  },
	  larger: {
	    2: `\\left(\${args[0]}${latexOperators.larger}\${args[1]}\\right)`
	  },
	  largerEq: {
	    2: `\\left(\${args[0]}${latexOperators.largerEq}\${args[1]}\\right)`
	  },
	  smaller: {
	    2: `\\left(\${args[0]}${latexOperators.smaller}\${args[1]}\\right)`
	  },
	  smallerEq: {
	    2: `\\left(\${args[0]}${latexOperators.smallerEq}\${args[1]}\\right)`
	  },
	  unequal: {
	    2: `\\left(\${args[0]}${latexOperators.unequal}\${args[1]}\\right)`
	  },
	  // special
	  erf: {
	    1: 'erf\\left(${args[0]}\\right)'
	  },
	  // statistics
	  max: '\\max\\left(${args}\\right)',
	  min: '\\min\\left(${args}\\right)',
	  variance: '\\mathrm{Var}\\left(${args}\\right)',
	  // trigonometry
	  acos: {
	    1: '\\cos^{-1}\\left(${args[0]}\\right)'
	  },
	  acosh: {
	    1: '\\cosh^{-1}\\left(${args[0]}\\right)'
	  },
	  acot: {
	    1: '\\cot^{-1}\\left(${args[0]}\\right)'
	  },
	  acoth: {
	    1: '\\coth^{-1}\\left(${args[0]}\\right)'
	  },
	  acsc: {
	    1: '\\csc^{-1}\\left(${args[0]}\\right)'
	  },
	  acsch: {
	    1: '\\mathrm{csch}^{-1}\\left(${args[0]}\\right)'
	  },
	  asec: {
	    1: '\\sec^{-1}\\left(${args[0]}\\right)'
	  },
	  asech: {
	    1: '\\mathrm{sech}^{-1}\\left(${args[0]}\\right)'
	  },
	  asin: {
	    1: '\\sin^{-1}\\left(${args[0]}\\right)'
	  },
	  asinh: {
	    1: '\\sinh^{-1}\\left(${args[0]}\\right)'
	  },
	  atan: {
	    1: '\\tan^{-1}\\left(${args[0]}\\right)'
	  },
	  atan2: {
	    2: '\\mathrm{atan2}\\left(${args}\\right)'
	  },
	  atanh: {
	    1: '\\tanh^{-1}\\left(${args[0]}\\right)'
	  },
	  cos: {
	    1: '\\cos\\left(${args[0]}\\right)'
	  },
	  cosh: {
	    1: '\\cosh\\left(${args[0]}\\right)'
	  },
	  cot: {
	    1: '\\cot\\left(${args[0]}\\right)'
	  },
	  coth: {
	    1: '\\coth\\left(${args[0]}\\right)'
	  },
	  csc: {
	    1: '\\csc\\left(${args[0]}\\right)'
	  },
	  csch: {
	    1: '\\mathrm{csch}\\left(${args[0]}\\right)'
	  },
	  sec: {
	    1: '\\sec\\left(${args[0]}\\right)'
	  },
	  sech: {
	    1: '\\mathrm{sech}\\left(${args[0]}\\right)'
	  },
	  sin: {
	    1: '\\sin\\left(${args[0]}\\right)'
	  },
	  sinh: {
	    1: '\\sinh\\left(${args[0]}\\right)'
	  },
	  tan: {
	    1: '\\tan\\left(${args[0]}\\right)'
	  },
	  tanh: {
	    1: '\\tanh\\left(${args[0]}\\right)'
	  },
	  // unit
	  to: {
	    2: `\\left(\${args[0]}${latexOperators.to}\${args[1]}\\right)`
	  },
	  // utils
	  numeric: function (node, options) {
	    // Not sure if this is strictly right but should work correctly for the vast majority of use cases.
	    return node.args[0].toTex();
	  },
	  // type
	  number: {
	    0: '0',
	    1: '\\left(${args[0]}\\right)',
	    2: '\\left(\\left(${args[0]}\\right)${args[1]}\\right)'
	  },
	  string: {
	    0: '\\mathtt{""}',
	    1: '\\mathrm{string}\\left(${args[0]}\\right)'
	  },
	  bignumber: {
	    0: '0',
	    1: '\\left(${args[0]}\\right)'
	  },
	  bigint: {
	    0: '0',
	    1: '\\left(${args[0]}\\right)'
	  },
	  complex: {
	    0: '0',
	    1: '\\left(${args[0]}\\right)',
	    2: `\\left(\\left(\${args[0]}\\right)+${latexSymbols.i}\\cdot\\left(\${args[1]}\\right)\\right)`
	  },
	  matrix: {
	    0: '\\begin{bmatrix}\\end{bmatrix}',
	    1: '\\left(${args[0]}\\right)',
	    2: '\\left(${args[0]}\\right)'
	  },
	  sparse: {
	    0: '\\begin{bsparse}\\end{bsparse}',
	    1: '\\left(${args[0]}\\right)'
	  },
	  unit: {
	    1: '\\left(${args[0]}\\right)',
	    2: '\\left(\\left(${args[0]}\\right)${args[1]}\\right)'
	  }
	};
	latex.defaultTemplate = '\\mathrm{${name}}\\left(${args}\\right)';
	const latexUnits = {
	  deg: '^\\circ'
	};
	function escapeLatex(string) {
	  return (0, _escapeLatex.default)(string, {
	    preserveFormatting: true
	  });
	}

	// @param {string} name
	// @param {boolean} isUnit
	function toSymbol(name, isUnit) {
	  isUnit = typeof isUnit === 'undefined' ? false : isUnit;
	  if (isUnit) {
	    if ((0, _object.hasOwnProperty)(latexUnits, name)) {
	      return latexUnits[name];
	    }
	    return '\\mathrm{' + escapeLatex(name) + '}';
	  }
	  if ((0, _object.hasOwnProperty)(latexSymbols, name)) {
	    return latexSymbols[name];
	  }
	  return escapeLatex(name);
	}
	return latex;
}

var hasRequiredConstantNode;

function requireConstantNode () {
	if (hasRequiredConstantNode) return ConstantNode;
	hasRequiredConstantNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(ConstantNode, "__esModule", {
	  value: true
	});
	ConstantNode.createConstantNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _string = requireString$2();
	var _is = requireIs();
	var _latex = requireLatex();
	var _factory = requireFactory$b();
	const name = 'ConstantNode';
	const dependencies = ['Node', 'isBounded'];
	ConstantNode.createConstantNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node,
	    isBounded
	  } = _ref;
	  class ConstantNode extends Node {
	    /**
	     * A ConstantNode holds a constant value like a number or string.
	     *
	     * Usage:
	     *
	     *     new ConstantNode(2.3)
	     *     new ConstantNode('hello')
	     *
	     * @param {*} value    Value can be any type (number, BigNumber, bigint, string, ...)
	     * @constructor ConstantNode
	     * @extends {Node}
	     */
	    constructor(value) {
	      super();
	      this.value = value;
	    }
	    get type() {
	      return name;
	    }
	    get isConstantNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const value = this.value;
	      return function evalConstantNode() {
	        return value;
	      };
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      // nothing to do, we don't have any children
	    }

	    /**
	     * Create a new ConstantNode with children produced by the given callback.
	     * Trivial because there are no children.
	     * @param {function(child: Node, path: string, parent: Node) : Node} callback
	     * @returns {ConstantNode} Returns a clone of the node
	     */
	    map(callback) {
	      return this.clone();
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {ConstantNode}
	     */
	    clone() {
	      return new ConstantNode(this.value);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      return (0, _string.format)(this.value, options);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const value = this._toString(options);
	      switch ((0, _is.typeOf)(this.value)) {
	        case 'number':
	        case 'bigint':
	        case 'BigNumber':
	        case 'Fraction':
	          return '<span class="math-number">' + value + '</span>';
	        case 'string':
	          return '<span class="math-string">' + value + '</span>';
	        case 'boolean':
	          return '<span class="math-boolean">' + value + '</span>';
	        case 'null':
	          return '<span class="math-null-symbol">' + value + '</span>';
	        case 'undefined':
	          return '<span class="math-undefined">' + value + '</span>';
	        default:
	          return '<span class="math-symbol">' + value + '</span>';
	      }
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        value: this.value
	      };
	    }

	    /**
	     * Instantiate a ConstantNode from its JSON representation
	     * @param {Object} json  An object structured like
	     *                       `{"mathjs": "SymbolNode", value: 2.3}`,
	     *                       where mathjs is optional
	     * @returns {ConstantNode}
	     */
	    static fromJSON(json) {
	      return new ConstantNode(json.value);
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const value = this._toString(options);
	      const type = (0, _is.typeOf)(this.value);
	      switch (type) {
	        case 'string':
	          return '\\mathtt{' + (0, _latex.escapeLatex)(value) + '}';
	        case 'number':
	        case 'BigNumber':
	          {
	            if (!isBounded(this.value)) {
	              return this.value.valueOf() < 0 ? '-\\infty' : '\\infty';
	            }
	            const index = value.toLowerCase().indexOf('e');
	            if (index !== -1) {
	              return value.substring(0, index) + '\\cdot10^{' + value.substring(index + 1) + '}';
	            }
	            return value;
	          }
	        case 'bigint':
	          {
	            return value.toString();
	          }
	        case 'Fraction':
	          return this.value.toLatex();
	        default:
	          return value;
	      }
	    }
	  }
	  (0, _defineProperty2.default)(ConstantNode, "name", name);
	  return ConstantNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return ConstantNode;
}

var FunctionAssignmentNode = {};

var hasRequiredFunctionAssignmentNode;

function requireFunctionAssignmentNode () {
	if (hasRequiredFunctionAssignmentNode) return FunctionAssignmentNode;
	hasRequiredFunctionAssignmentNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(FunctionAssignmentNode, "__esModule", {
	  value: true
	});
	FunctionAssignmentNode.createFunctionAssignmentNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _keywords = requireKeywords();
	var _string = requireString$2();
	var _array = requireArray();
	var _latex = requireLatex();
	var _operators = requireOperators();
	var _factory = requireFactory$b();
	const name = 'FunctionAssignmentNode';
	const dependencies = ['typed', 'Node'];
	FunctionAssignmentNode.createFunctionAssignmentNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Node
	  } = _ref;
	  /**
	   * Is parenthesis needed?
	   * @param {Node} node
	   * @param {Object} parenthesis
	   * @param {string} implicit
	   * @private
	   */
	  function needParenthesis(node, parenthesis, implicit) {
	    const precedence = (0, _operators.getPrecedence)(node, parenthesis, implicit);
	    const exprPrecedence = (0, _operators.getPrecedence)(node.expr, parenthesis, implicit);
	    return parenthesis === 'all' || exprPrecedence !== null && exprPrecedence <= precedence;
	  }
	  class FunctionAssignmentNode extends Node {
	    /**
	     * @constructor FunctionAssignmentNode
	     * @extends {Node}
	     * Function assignment
	     *
	     * @param {string} name           Function name
	     * @param {string[] | Array.<{name: string, type: string}>} params
	     *                                Array with function parameter names, or an
	     *                                array with objects containing the name
	     *                                and type of the parameter
	     * @param {Node} expr             The function expression
	     */
	    constructor(name, params, expr) {
	      super();
	      // validate input
	      if (typeof name !== 'string') {
	        throw new TypeError('String expected for parameter "name"');
	      }
	      if (!Array.isArray(params)) {
	        throw new TypeError('Array containing strings or objects expected for parameter "params"');
	      }
	      if (!(0, _is.isNode)(expr)) {
	        throw new TypeError('Node expected for parameter "expr"');
	      }
	      if (_keywords.keywords.has(name)) {
	        throw new Error('Illegal function name, "' + name + '" is a reserved keyword');
	      }
	      const paramNames = new Set();
	      for (const param of params) {
	        const name = typeof param === 'string' ? param : param.name;
	        if (paramNames.has(name)) {
	          throw new Error(`Duplicate parameter name "${name}"`);
	        } else {
	          paramNames.add(name);
	        }
	      }
	      this.name = name;
	      this.params = params.map(function (param) {
	        return param && param.name || param;
	      });
	      this.types = params.map(function (param) {
	        return param && param.type || 'any';
	      });
	      this.expr = expr;
	    }
	    get type() {
	      return name;
	    }
	    get isFunctionAssignmentNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const childArgNames = Object.create(argNames);
	      (0, _array.forEach)(this.params, function (param) {
	        childArgNames[param] = true;
	      });

	      // compile the function expression with the child args
	      const expr = this.expr;
	      const evalExpr = expr._compile(math, childArgNames);
	      const name = this.name;
	      const params = this.params;
	      const signature = (0, _array.join)(this.types, ',');
	      const syntax = name + '(' + (0, _array.join)(this.params, ', ') + ')';
	      return function evalFunctionAssignmentNode(scope, args, context) {
	        const signatures = {};
	        signatures[signature] = function () {
	          const childArgs = Object.create(args);
	          for (let i = 0; i < params.length; i++) {
	            childArgs[params[i]] = arguments[i];
	          }
	          return evalExpr(scope, childArgs, context);
	        };
	        const fn = typed(name, signatures);
	        fn.syntax = syntax;
	        fn.expr = expr.toString();
	        scope.set(name, fn);
	        return fn;
	      };
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.expr, 'expr', this);
	    }

	    /**
	     * Create a new FunctionAssignmentNode whose children are the results of
	     * calling the provided callback function for each child of the original
	     * node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {FunctionAssignmentNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const expr = this._ifNode(callback(this.expr, 'expr', this));
	      return new FunctionAssignmentNode(this.name, this.params.slice(0), expr);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {FunctionAssignmentNode}
	     */
	    clone() {
	      return new FunctionAssignmentNode(this.name, this.params.slice(0), this.expr);
	    }

	    /**
	     * get string representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      let expr = this.expr.toString(options);
	      if (needParenthesis(this, parenthesis, options && options.implicit)) {
	        expr = '(' + expr + ')';
	      }
	      return this.name + '(' + this.params.join(', ') + ') = ' + expr;
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      const types = this.types;
	      return {
	        mathjs: name,
	        name: this.name,
	        params: this.params.map(function (param, index) {
	          return {
	            name: param,
	            type: types[index]
	          };
	        }),
	        expr: this.expr
	      };
	    }

	    /**
	     * Instantiate an FunctionAssignmentNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     ```
	     *     {"mathjs": "FunctionAssignmentNode",
	     *      name: ..., params: ..., expr: ...}
	     *     ```
	     *     where mathjs is optional
	     * @returns {FunctionAssignmentNode}
	     */
	    static fromJSON(json) {
	      return new FunctionAssignmentNode(json.name, json.params, json.expr);
	    }

	    /**
	     * get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const params = [];
	      for (let i = 0; i < this.params.length; i++) {
	        params.push('<span class="math-symbol math-parameter">' + (0, _string.escape)(this.params[i]) + '</span>');
	      }
	      let expr = this.expr.toHTML(options);
	      if (needParenthesis(this, parenthesis, options && options.implicit)) {
	        expr = '<span class="math-parenthesis math-round-parenthesis">(</span>' + expr + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      return '<span class="math-function">' + (0, _string.escape)(this.name) + '</span>' + '<span class="math-parenthesis math-round-parenthesis">(</span>' + params.join('<span class="math-separator">,</span>') + '<span class="math-parenthesis math-round-parenthesis">)</span>' + '<span class="math-operator math-assignment-operator ' + 'math-variable-assignment-operator math-binary-operator">=</span>' + expr;
	    }

	    /**
	     * get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      let expr = this.expr.toTex(options);
	      if (needParenthesis(this, parenthesis, options && options.implicit)) {
	        expr = `\\left(${expr}\\right)`;
	      }
	      return '\\mathrm{' + this.name + '}\\left(' + this.params.map(_latex.toSymbol).join(',') + '\\right)=' + expr;
	    }
	  }
	  (0, _defineProperty2.default)(FunctionAssignmentNode, "name", name);
	  return FunctionAssignmentNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return FunctionAssignmentNode;
}

var IndexNode = {};

var hasRequiredIndexNode;

function requireIndexNode () {
	if (hasRequiredIndexNode) return IndexNode;
	hasRequiredIndexNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(IndexNode, "__esModule", {
	  value: true
	});
	IndexNode.createIndexNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _array = requireArray();
	var _customs = requireCustoms();
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _string = requireString$2();
	const name = 'IndexNode';
	const dependencies = ['Node', 'size'];
	IndexNode.createIndexNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node,
	    size
	  } = _ref;
	  class IndexNode extends Node {
	    /**
	     * @constructor IndexNode
	     * @extends Node
	     *
	     * Describes a subset of a matrix or an object property.
	     * Cannot be used on its own, needs to be used within an AccessorNode or
	     * AssignmentNode.
	     *
	     * @param {Node[]} dimensions
	     * @param {boolean} [dotNotation=false]
	     *     Optional property describing whether this index was written using dot
	     *     notation like `a.b`, or using bracket notation like `a["b"]`
	     *     (which is the default). This property is used for string conversion.
	     */
	    constructor(dimensions, dotNotation) {
	      super();
	      this.dimensions = dimensions;
	      this.dotNotation = dotNotation || false;

	      // validate input
	      if (!Array.isArray(dimensions) || !dimensions.every(_is.isNode)) {
	        throw new TypeError('Array containing Nodes expected for parameter "dimensions"');
	      }
	      if (this.dotNotation && !this.isObjectProperty()) {
	        throw new Error('dotNotation only applicable for object properties');
	      }
	    }
	    get type() {
	      return name;
	    }
	    get isIndexNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      // TODO: implement support for bignumber (currently bignumbers are silently
	      //       reduced to numbers when changing the value to zero-based)

	      // TODO: Optimization: when the range values are ConstantNodes,
	      //       we can beforehand resolve the zero-based value

	      // optimization for a simple object property
	      const evalDimensions = (0, _array.map)(this.dimensions, function (dimension, i) {
	        const needsEnd = dimension.filter(node => node.isSymbolNode && node.name === 'end').length > 0;
	        if (needsEnd) {
	          // SymbolNode 'end' is used inside the index,
	          // like in `A[end]` or `A[end - 2]`
	          const childArgNames = Object.create(argNames);
	          childArgNames.end = true;
	          const _evalDimension = dimension._compile(math, childArgNames);
	          return function evalDimension(scope, args, context) {
	            if (!(0, _is.isMatrix)(context) && !(0, _is.isArray)(context) && !(0, _is.isString)(context)) {
	              throw new TypeError('Cannot resolve "end": ' + 'context must be a Matrix, Array, or string but is ' + (0, _is.typeOf)(context));
	            }
	            const s = size(context);
	            const childArgs = Object.create(args);
	            childArgs.end = s[i];
	            return _evalDimension(scope, childArgs, context);
	          };
	        } else {
	          // SymbolNode `end` not used
	          return dimension._compile(math, argNames);
	        }
	      });
	      const index = (0, _customs.getSafeProperty)(math, 'index');
	      return function evalIndexNode(scope, args, context) {
	        const dimensions = (0, _array.map)(evalDimensions, function (evalDimension) {
	          return evalDimension(scope, args, context);
	        });
	        return index(...dimensions);
	      };
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      for (let i = 0; i < this.dimensions.length; i++) {
	        callback(this.dimensions[i], 'dimensions[' + i + ']', this);
	      }
	    }

	    /**
	     * Create a new IndexNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {IndexNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const dimensions = [];
	      for (let i = 0; i < this.dimensions.length; i++) {
	        dimensions[i] = this._ifNode(callback(this.dimensions[i], 'dimensions[' + i + ']', this));
	      }
	      return new IndexNode(dimensions, this.dotNotation);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {IndexNode}
	     */
	    clone() {
	      return new IndexNode(this.dimensions.slice(0), this.dotNotation);
	    }

	    /**
	     * Test whether this IndexNode contains a single property name
	     * @return {boolean}
	     */
	    isObjectProperty() {
	      return this.dimensions.length === 1 && (0, _is.isConstantNode)(this.dimensions[0]) && typeof this.dimensions[0].value === 'string';
	    }

	    /**
	     * Returns the property name if IndexNode contains a property.
	     * If not, returns null.
	     * @return {string | null}
	     */
	    getObjectProperty() {
	      return this.isObjectProperty() ? this.dimensions[0].value : null;
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      // format the parameters like "[1, 0:5]"
	      return this.dotNotation ? '.' + this.getObjectProperty() : '[' + this.dimensions.join(', ') + ']';
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        dimensions: this.dimensions,
	        dotNotation: this.dotNotation
	      };
	    }

	    /**
	     * Instantiate an IndexNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     `{"mathjs": "IndexNode", dimensions: [...], dotNotation: false}`,
	     *     where mathjs is optional
	     * @returns {IndexNode}
	     */
	    static fromJSON(json) {
	      return new IndexNode(json.dimensions, json.dotNotation);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      // format the parameters like "[1, 0:5]"
	      const dimensions = [];
	      for (let i = 0; i < this.dimensions.length; i++) {
	        dimensions[i] = this.dimensions[i].toHTML();
	      }
	      if (this.dotNotation) {
	        return '<span class="math-operator math-accessor-operator">.</span>' + '<span class="math-symbol math-property">' + (0, _string.escape)(this.getObjectProperty()) + '</span>';
	      } else {
	        return '<span class="math-parenthesis math-square-parenthesis">[</span>' + dimensions.join('<span class="math-separator">,</span>') + '<span class="math-parenthesis math-square-parenthesis">]</span>';
	      }
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const dimensions = this.dimensions.map(function (range) {
	        return range.toTex(options);
	      });
	      return this.dotNotation ? '.' + this.getObjectProperty() + '' : '_{' + dimensions.join(',') + '}';
	    }
	  }
	  (0, _defineProperty2.default)(IndexNode, "name", name);
	  return IndexNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return IndexNode;
}

var ObjectNode = {};

var hasRequiredObjectNode;

function requireObjectNode () {
	if (hasRequiredObjectNode) return ObjectNode;
	hasRequiredObjectNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(ObjectNode, "__esModule", {
	  value: true
	});
	ObjectNode.createObjectNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _customs = requireCustoms();
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _object = requireObject$3();
	var _string = requireString$2();
	const name = 'ObjectNode';
	const dependencies = ['Node'];
	ObjectNode.createObjectNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  class ObjectNode extends Node {
	    /**
	     * @constructor ObjectNode
	     * @extends {Node}
	     * Holds an object with keys/values
	     * @param {Object.<string, Node>} [properties]   object with key/value pairs
	     */
	    constructor(properties) {
	      super();
	      this.properties = properties || {};

	      // validate input
	      if (properties) {
	        if (!(typeof properties === 'object') || !Object.keys(properties).every(function (key) {
	          return (0, _is.isNode)(properties[key]);
	        })) {
	          throw new TypeError('Object containing Nodes expected');
	        }
	      }
	    }
	    get type() {
	      return name;
	    }
	    get isObjectNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const evalEntries = {};
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          // we stringify/parse the key here to resolve unicode characters,
	          // so you cannot create a key like {"co\\u006Estructor": null}
	          const stringifiedKey = (0, _string.stringify)(key);
	          const parsedKey = JSON.parse(stringifiedKey);
	          const prop = (0, _customs.getSafeProperty)(this.properties, key);
	          evalEntries[parsedKey] = prop._compile(math, argNames);
	        }
	      }
	      return function evalObjectNode(scope, args, context) {
	        const obj = {};
	        for (const key in evalEntries) {
	          if ((0, _object.hasOwnProperty)(evalEntries, key)) {
	            obj[key] = evalEntries[key](scope, args, context);
	          }
	        }
	        return obj;
	      };
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          callback(this.properties[key], 'properties[' + (0, _string.stringify)(key) + ']', this);
	        }
	      }
	    }

	    /**
	     * Create a new ObjectNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {ObjectNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const properties = {};
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          properties[key] = this._ifNode(callback(this.properties[key], 'properties[' + (0, _string.stringify)(key) + ']', this));
	        }
	      }
	      return new ObjectNode(properties);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {ObjectNode}
	     */
	    clone() {
	      const properties = {};
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          properties[key] = this.properties[key];
	        }
	      }
	      return new ObjectNode(properties);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toString(options) {
	      const entries = [];
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          entries.push((0, _string.stringify)(key) + ': ' + this.properties[key].toString(options));
	        }
	      }
	      return '{' + entries.join(', ') + '}';
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        properties: this.properties
	      };
	    }

	    /**
	     * Instantiate an OperatorNode from its JSON representation
	     * @param {Object} json  An object structured like
	     *                       `{"mathjs": "ObjectNode", "properties": {...}}`,
	     *                       where mathjs is optional
	     * @returns {ObjectNode}
	     */
	    static fromJSON(json) {
	      return new ObjectNode(json.properties);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toHTML(options) {
	      const entries = [];
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          entries.push('<span class="math-symbol math-property">' + (0, _string.escape)(key) + '</span>' + '<span class="math-operator math-assignment-operator ' + 'math-property-assignment-operator math-binary-operator">' + ':</span>' + this.properties[key].toHTML(options));
	        }
	      }
	      return '<span class="math-parenthesis math-curly-parenthesis">{</span>' + entries.join('<span class="math-separator">,</span>') + '<span class="math-parenthesis math-curly-parenthesis">}</span>';
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const entries = [];
	      for (const key in this.properties) {
	        if ((0, _object.hasOwnProperty)(this.properties, key)) {
	          entries.push('\\mathbf{' + key + ':} & ' + this.properties[key].toTex(options) + '\\\\');
	        }
	      }
	      const tex = '\\left\\{\\begin{array}{ll}' + entries.join('\n') + '\\end{array}\\right\\}';
	      return tex;
	    }
	  }
	  (0, _defineProperty2.default)(ObjectNode, "name", name);
	  return ObjectNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return ObjectNode;
}

var OperatorNode = {};

var scope = {};

var hasRequiredScope;

function requireScope () {
	if (hasRequiredScope) return scope;
	hasRequiredScope = 1;

	Object.defineProperty(scope, "__esModule", {
	  value: true
	});
	scope.createSubScope = createSubScope;
	var _map = requireMap$2();
	/**
	 * Create a new scope which can access the parent scope,
	 * but does not affect it when written. This is suitable for variable definitions
	 * within a block node, or function definition.
	 *
	 * If parent scope has a createSubScope method, it delegates to that. Otherwise,
	 * creates an empty map, and copies the parent scope to it, adding in
	 * the remaining `args`.
	 *
	 * @param {Map} parentScope
	 * @param  {Object} args
	 * @returns {PartitionedMap}
	 */
	function createSubScope(parentScope, args) {
	  return new _map.PartitionedMap(parentScope, new _map.ObjectWrappingMap(args), new Set(Object.keys(args)));
	}
	return scope;
}

var hasRequiredOperatorNode;

function requireOperatorNode () {
	if (hasRequiredOperatorNode) return OperatorNode;
	hasRequiredOperatorNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(OperatorNode, "__esModule", {
	  value: true
	});
	OperatorNode.createOperatorNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _array = requireArray();
	var _scope = requireScope();
	var _string = requireString$2();
	var _customs = requireCustoms();
	var _operators = requireOperators();
	var _latex = requireLatex();
	var _factory = requireFactory$b();
	const name = 'OperatorNode';
	const dependencies = ['Node'];
	OperatorNode.createOperatorNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  /**
	   * Returns true if the expression starts with a constant, under
	   * the current parenthesization:
	   * @param {Node} expression
	   * @param {string} parenthesis
	   * @return {boolean}
	   */
	  function startsWithConstant(expr, parenthesis) {
	    let curNode = expr;
	    if (parenthesis === 'auto') {
	      while ((0, _is.isParenthesisNode)(curNode)) curNode = curNode.content;
	    }
	    if ((0, _is.isConstantNode)(curNode)) return true;
	    if ((0, _is.isOperatorNode)(curNode)) {
	      return startsWithConstant(curNode.args[0], parenthesis);
	    }
	    return false;
	  }

	  /**
	   * Calculate which parentheses are necessary. Gets an OperatorNode
	   * (which is the root of the tree) and an Array of Nodes
	   * (this.args) and returns an array where 'true' means that an argument
	   * has to be enclosed in parentheses whereas 'false' means the opposite.
	   *
	   * @param {OperatorNode} root
	   * @param {string} parenthesis
	   * @param {Node[]} args
	   * @param {boolean} latex
	   * @return {boolean[]}
	   * @private
	   */
	  function calculateNecessaryParentheses(root, parenthesis, implicit, args, latex) {
	    // precedence of the root OperatorNode
	    const precedence = (0, _operators.getPrecedence)(root, parenthesis, implicit);
	    const associativity = (0, _operators.getAssociativity)(root, parenthesis);
	    if (parenthesis === 'all' || args.length > 2 && root.getIdentifier() !== 'OperatorNode:add' && root.getIdentifier() !== 'OperatorNode:multiply') {
	      return args.map(function (arg) {
	        switch (arg.getContent().type) {
	          // Nodes that don't need extra parentheses
	          case 'ArrayNode':
	          case 'ConstantNode':
	          case 'SymbolNode':
	          case 'ParenthesisNode':
	            return false;
	          default:
	            return true;
	        }
	      });
	    }
	    let result;
	    switch (args.length) {
	      case 0:
	        result = [];
	        break;
	      case 1:
	        // unary operators
	        {
	          // precedence of the operand
	          const operandPrecedence = (0, _operators.getPrecedence)(args[0], parenthesis, implicit, root);

	          // handle special cases for LaTeX, where some of the parentheses aren't needed
	          if (latex && operandPrecedence !== null) {
	            let operandIdentifier;
	            let rootIdentifier;
	            if (parenthesis === 'keep') {
	              operandIdentifier = args[0].getIdentifier();
	              rootIdentifier = root.getIdentifier();
	            } else {
	              // Ignore Parenthesis Nodes when not in 'keep' mode
	              operandIdentifier = args[0].getContent().getIdentifier();
	              rootIdentifier = root.getContent().getIdentifier();
	            }
	            if (_operators.properties[precedence][rootIdentifier].latexLeftParens === false) {
	              result = [false];
	              break;
	            }
	            if (_operators.properties[operandPrecedence][operandIdentifier].latexParens === false) {
	              result = [false];
	              break;
	            }
	          }
	          if (operandPrecedence === null) {
	            // if the operand has no defined precedence, no parens are needed
	            result = [false];
	            break;
	          }
	          if (operandPrecedence <= precedence) {
	            // if the operands precedence is lower, parens are needed
	            result = [true];
	            break;
	          }

	          // otherwise, no parens needed
	          result = [false];
	        }
	        break;
	      case 2:
	        // binary operators
	        {
	          let lhsParens; // left hand side needs parenthesis?
	          // precedence of the left hand side
	          const lhsPrecedence = (0, _operators.getPrecedence)(args[0], parenthesis, implicit, root);
	          // is the root node associative with the left hand side
	          const assocWithLhs = (0, _operators.isAssociativeWith)(root, args[0], parenthesis);
	          if (lhsPrecedence === null) {
	            // if the left hand side has no defined precedence, no parens are needed
	            // FunctionNode for example
	            lhsParens = false;
	          } else if (lhsPrecedence === precedence && associativity === 'right' && !assocWithLhs) {
	            // In case of equal precedence, if the root node is left associative
	            // parens are **never** necessary for the left hand side.
	            // If it is right associative however, parens are necessary
	            // if the root node isn't associative with the left hand side
	            lhsParens = true;
	          } else if (lhsPrecedence < precedence) {
	            lhsParens = true;
	          } else {
	            lhsParens = false;
	          }
	          let rhsParens; // right hand side needs parenthesis?
	          // precedence of the right hand side
	          const rhsPrecedence = (0, _operators.getPrecedence)(args[1], parenthesis, implicit, root);
	          // is the root node associative with the right hand side?
	          const assocWithRhs = (0, _operators.isAssociativeWith)(root, args[1], parenthesis);
	          if (rhsPrecedence === null) {
	            // if the right hand side has no defined precedence, no parens are needed
	            // FunctionNode for example
	            rhsParens = false;
	          } else if (rhsPrecedence === precedence && associativity === 'left' && !assocWithRhs) {
	            // In case of equal precedence, if the root node is right associative
	            // parens are **never** necessary for the right hand side.
	            // If it is left associative however, parens are necessary
	            // if the root node isn't associative with the right hand side
	            rhsParens = true;
	          } else if (rhsPrecedence < precedence) {
	            rhsParens = true;
	          } else {
	            rhsParens = false;
	          }

	          // handle special cases for LaTeX, where some of the parentheses aren't needed
	          if (latex) {
	            let rootIdentifier;
	            let lhsIdentifier;
	            let rhsIdentifier;
	            if (parenthesis === 'keep') {
	              rootIdentifier = root.getIdentifier();
	              lhsIdentifier = root.args[0].getIdentifier();
	              rhsIdentifier = root.args[1].getIdentifier();
	            } else {
	              // Ignore ParenthesisNodes when not in 'keep' mode
	              rootIdentifier = root.getContent().getIdentifier();
	              lhsIdentifier = root.args[0].getContent().getIdentifier();
	              rhsIdentifier = root.args[1].getContent().getIdentifier();
	            }
	            if (lhsPrecedence !== null) {
	              if (_operators.properties[precedence][rootIdentifier].latexLeftParens === false) {
	                lhsParens = false;
	              }
	              if (_operators.properties[lhsPrecedence][lhsIdentifier].latexParens === false) {
	                lhsParens = false;
	              }
	            }
	            if (rhsPrecedence !== null) {
	              if (_operators.properties[precedence][rootIdentifier].latexRightParens === false) {
	                rhsParens = false;
	              }
	              if (_operators.properties[rhsPrecedence][rhsIdentifier].latexParens === false) {
	                rhsParens = false;
	              }
	            }
	          }
	          result = [lhsParens, rhsParens];
	        }
	        break;
	      default:
	        if (root.getIdentifier() === 'OperatorNode:add' || root.getIdentifier() === 'OperatorNode:multiply') {
	          result = args.map(function (arg) {
	            const argPrecedence = (0, _operators.getPrecedence)(arg, parenthesis, implicit, root);
	            const assocWithArg = (0, _operators.isAssociativeWith)(root, arg, parenthesis);
	            const argAssociativity = (0, _operators.getAssociativity)(arg, parenthesis);
	            if (argPrecedence === null) {
	              // if the argument has no defined precedence, no parens are needed
	              return false;
	            } else if (precedence === argPrecedence && associativity === argAssociativity && !assocWithArg) {
	              return true;
	            } else if (argPrecedence < precedence) {
	              return true;
	            }
	            return false;
	          });
	        }
	        break;
	    }

	    // Handles an edge case of parentheses with implicit multiplication
	    // of ConstantNode.
	    // In that case, parenthesize ConstantNodes that follow an unparenthesized
	    // expression, even though they normally wouldn't be printed.
	    if (args.length >= 2 && root.getIdentifier() === 'OperatorNode:multiply' && root.implicit && parenthesis !== 'all' && implicit === 'hide') {
	      for (let i = 1; i < result.length; ++i) {
	        if (startsWithConstant(args[i], parenthesis) && !result[i - 1] && (parenthesis !== 'keep' || !(0, _is.isParenthesisNode)(args[i - 1]))) {
	          result[i] = true;
	        }
	      }
	    }
	    return result;
	  }
	  class OperatorNode extends Node {
	    /**
	     * @constructor OperatorNode
	     * @extends {Node}
	     * An operator with two arguments, like 2+3
	     *
	     * @param {string} op           Operator name, for example '+'
	     * @param {string} fn           Function name, for example 'add'
	     * @param {Node[]} args         Operator arguments
	     * @param {boolean} [implicit]  Is this an implicit multiplication?
	     * @param {boolean} [isPercentage] Is this an percentage Operation?
	     */
	    constructor(op, fn, args, implicit, isPercentage) {
	      super();
	      // validate input
	      if (typeof op !== 'string') {
	        throw new TypeError('string expected for parameter "op"');
	      }
	      if (typeof fn !== 'string') {
	        throw new TypeError('string expected for parameter "fn"');
	      }
	      if (!Array.isArray(args) || !args.every(_is.isNode)) {
	        throw new TypeError('Array containing Nodes expected for parameter "args"');
	      }
	      this.implicit = implicit === true;
	      this.isPercentage = isPercentage === true;
	      this.op = op;
	      this.fn = fn;
	      this.args = args || [];
	    }
	    get type() {
	      return name;
	    }
	    get isOperatorNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      // validate fn
	      if (typeof this.fn !== 'string' || !(0, _customs.isSafeMethod)(math, this.fn)) {
	        if (!math[this.fn]) {
	          throw new Error('Function ' + this.fn + ' missing in provided namespace "math"');
	        } else {
	          throw new Error('No access to function "' + this.fn + '"');
	        }
	      }
	      const fn = (0, _customs.getSafeProperty)(math, this.fn);
	      const evalArgs = (0, _array.map)(this.args, function (arg) {
	        return arg._compile(math, argNames);
	      });
	      if (typeof fn === 'function' && fn.rawArgs === true) {
	        // pass unevaluated parameters (nodes) to the function
	        // "raw" evaluation
	        const rawArgs = this.args;
	        return function evalOperatorNode(scope, args, context) {
	          return fn(rawArgs, math, (0, _scope.createSubScope)(scope, args));
	        };
	      } else if (evalArgs.length === 1) {
	        const evalArg0 = evalArgs[0];
	        return function evalOperatorNode(scope, args, context) {
	          return fn(evalArg0(scope, args, context));
	        };
	      } else if (evalArgs.length === 2) {
	        const evalArg0 = evalArgs[0];
	        const evalArg1 = evalArgs[1];
	        return function evalOperatorNode(scope, args, context) {
	          return fn(evalArg0(scope, args, context), evalArg1(scope, args, context));
	        };
	      } else {
	        return function evalOperatorNode(scope, args, context) {
	          return fn.apply(null, (0, _array.map)(evalArgs, function (evalArg) {
	            return evalArg(scope, args, context);
	          }));
	        };
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      for (let i = 0; i < this.args.length; i++) {
	        callback(this.args[i], 'args[' + i + ']', this);
	      }
	    }

	    /**
	     * Create a new OperatorNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {OperatorNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const args = [];
	      for (let i = 0; i < this.args.length; i++) {
	        args[i] = this._ifNode(callback(this.args[i], 'args[' + i + ']', this));
	      }
	      return new OperatorNode(this.op, this.fn, args, this.implicit, this.isPercentage);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {OperatorNode}
	     */
	    clone() {
	      return new OperatorNode(this.op, this.fn, this.args.slice(0), this.implicit, this.isPercentage);
	    }

	    /**
	     * Check whether this is an unary OperatorNode:
	     * has exactly one argument, like `-a`.
	     * @return {boolean}
	     *     Returns true when an unary operator node, false otherwise.
	     */
	    isUnary() {
	      return this.args.length === 1;
	    }

	    /**
	     * Check whether this is a binary OperatorNode:
	     * has exactly two arguments, like `a + b`.
	     * @return {boolean}
	     *     Returns true when a binary operator node, false otherwise.
	     */
	    isBinary() {
	      return this.args.length === 2;
	    }

	    /**
	     * Get string representation.
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const implicit = options && options.implicit ? options.implicit : 'hide';
	      const args = this.args;
	      const parens = calculateNecessaryParentheses(this, parenthesis, implicit, args, false);
	      if (args.length === 1) {
	        // unary operators
	        const assoc = (0, _operators.getAssociativity)(this, parenthesis);
	        let operand = args[0].toString(options);
	        if (parens[0]) {
	          operand = '(' + operand + ')';
	        }

	        // for example for "not", we want a space between operand and argument
	        const opIsNamed = /[a-zA-Z]+/.test(this.op);
	        if (assoc === 'right') {
	          // prefix operator
	          return this.op + (opIsNamed ? ' ' : '') + operand;
	        } else if (assoc === 'left') {
	          // postfix
	          return operand + (opIsNamed ? ' ' : '') + this.op;
	        }

	        // fall back to postfix
	        return operand + this.op;
	      } else if (args.length === 2) {
	        let lhs = args[0].toString(options); // left hand side
	        let rhs = args[1].toString(options); // right hand side
	        if (parens[0]) {
	          // left hand side in parenthesis?
	          lhs = '(' + lhs + ')';
	        }
	        if (parens[1]) {
	          // right hand side in parenthesis?
	          rhs = '(' + rhs + ')';
	        }
	        if (this.implicit && this.getIdentifier() === 'OperatorNode:multiply' && implicit === 'hide') {
	          return lhs + ' ' + rhs;
	        }
	        return lhs + ' ' + this.op + ' ' + rhs;
	      } else if (args.length > 2 && (this.getIdentifier() === 'OperatorNode:add' || this.getIdentifier() === 'OperatorNode:multiply')) {
	        const stringifiedArgs = args.map(function (arg, index) {
	          arg = arg.toString(options);
	          if (parens[index]) {
	            // put in parenthesis?
	            arg = '(' + arg + ')';
	          }
	          return arg;
	        });
	        if (this.implicit && this.getIdentifier() === 'OperatorNode:multiply' && implicit === 'hide') {
	          return stringifiedArgs.join(' ');
	        }
	        return stringifiedArgs.join(' ' + this.op + ' ');
	      } else {
	        // fallback to formatting as a function call
	        return this.fn + '(' + this.args.join(', ') + ')';
	      }
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        op: this.op,
	        fn: this.fn,
	        args: this.args,
	        implicit: this.implicit,
	        isPercentage: this.isPercentage
	      };
	    }

	    /**
	     * Instantiate an OperatorNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     ```
	     *     {"mathjs": "OperatorNode",
	     *      "op": "+", "fn": "add", "args": [...],
	     *      "implicit": false,
	     *      "isPercentage":false}
	     *     ```
	     *     where mathjs is optional
	     * @returns {OperatorNode}
	     */
	    static fromJSON(json) {
	      return new OperatorNode(json.op, json.fn, json.args, json.implicit, json.isPercentage);
	    }

	    /**
	     * Get HTML representation.
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const implicit = options && options.implicit ? options.implicit : 'hide';
	      const args = this.args;
	      const parens = calculateNecessaryParentheses(this, parenthesis, implicit, args, false);
	      if (args.length === 1) {
	        // unary operators
	        const assoc = (0, _operators.getAssociativity)(this, parenthesis);
	        let operand = args[0].toHTML(options);
	        if (parens[0]) {
	          operand = '<span class="math-parenthesis math-round-parenthesis">(</span>' + operand + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	        }
	        if (assoc === 'right') {
	          // prefix operator
	          return '<span class="math-operator math-unary-operator ' + 'math-lefthand-unary-operator">' + (0, _string.escape)(this.op) + '</span>' + operand;
	        } else {
	          // postfix when assoc === 'left' or undefined
	          return operand + '<span class="math-operator math-unary-operator ' + 'math-righthand-unary-operator">' + (0, _string.escape)(this.op) + '</span>';
	        }
	      } else if (args.length === 2) {
	        // binary operatoes
	        let lhs = args[0].toHTML(options); // left hand side
	        let rhs = args[1].toHTML(options); // right hand side
	        if (parens[0]) {
	          // left hand side in parenthesis?
	          lhs = '<span class="math-parenthesis math-round-parenthesis">(</span>' + lhs + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	        }
	        if (parens[1]) {
	          // right hand side in parenthesis?
	          rhs = '<span class="math-parenthesis math-round-parenthesis">(</span>' + rhs + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	        }
	        if (this.implicit && this.getIdentifier() === 'OperatorNode:multiply' && implicit === 'hide') {
	          return lhs + '<span class="math-operator math-binary-operator ' + 'math-implicit-binary-operator"></span>' + rhs;
	        }
	        return lhs + '<span class="math-operator math-binary-operator ' + 'math-explicit-binary-operator">' + (0, _string.escape)(this.op) + '</span>' + rhs;
	      } else {
	        const stringifiedArgs = args.map(function (arg, index) {
	          arg = arg.toHTML(options);
	          if (parens[index]) {
	            // put in parenthesis?
	            arg = '<span class="math-parenthesis math-round-parenthesis">(</span>' + arg + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	          }
	          return arg;
	        });
	        if (args.length > 2 && (this.getIdentifier() === 'OperatorNode:add' || this.getIdentifier() === 'OperatorNode:multiply')) {
	          if (this.implicit && this.getIdentifier() === 'OperatorNode:multiply' && implicit === 'hide') {
	            return stringifiedArgs.join('<span class="math-operator math-binary-operator ' + 'math-implicit-binary-operator"></span>');
	          }
	          return stringifiedArgs.join('<span class="math-operator math-binary-operator ' + 'math-explicit-binary-operator">' + (0, _string.escape)(this.op) + '</span>');
	        } else {
	          // fallback to formatting as a function call
	          return '<span class="math-function">' + (0, _string.escape)(this.fn) + '</span><span class="math-paranthesis math-round-parenthesis">' + '(</span>' + stringifiedArgs.join('<span class="math-separator">,</span>') + '<span class="math-paranthesis math-round-parenthesis">)</span>';
	        }
	      }
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const implicit = options && options.implicit ? options.implicit : 'hide';
	      const args = this.args;
	      const parens = calculateNecessaryParentheses(this, parenthesis, implicit, args, true);
	      let op = _latex.latexOperators[this.fn];
	      op = typeof op === 'undefined' ? this.op : op; // fall back to using this.op

	      if (args.length === 1) {
	        // unary operators
	        const assoc = (0, _operators.getAssociativity)(this, parenthesis);
	        let operand = args[0].toTex(options);
	        if (parens[0]) {
	          operand = `\\left(${operand}\\right)`;
	        }
	        if (assoc === 'right') {
	          // prefix operator
	          return op + operand;
	        } else if (assoc === 'left') {
	          // postfix operator
	          return operand + op;
	        }

	        // fall back to postfix
	        return operand + op;
	      } else if (args.length === 2) {
	        // binary operators
	        const lhs = args[0]; // left hand side
	        let lhsTex = lhs.toTex(options);
	        if (parens[0]) {
	          lhsTex = `\\left(${lhsTex}\\right)`;
	        }
	        const rhs = args[1]; // right hand side
	        let rhsTex = rhs.toTex(options);
	        if (parens[1]) {
	          rhsTex = `\\left(${rhsTex}\\right)`;
	        }

	        // handle some exceptions (due to the way LaTeX works)
	        let lhsIdentifier;
	        if (parenthesis === 'keep') {
	          lhsIdentifier = lhs.getIdentifier();
	        } else {
	          // Ignore ParenthesisNodes if in 'keep' mode
	          lhsIdentifier = lhs.getContent().getIdentifier();
	        }
	        switch (this.getIdentifier()) {
	          case 'OperatorNode:divide':
	            // op contains '\\frac' at this point
	            return op + '{' + lhsTex + '}' + '{' + rhsTex + '}';
	          case 'OperatorNode:pow':
	            lhsTex = '{' + lhsTex + '}';
	            rhsTex = '{' + rhsTex + '}';
	            switch (lhsIdentifier) {
	              case 'ConditionalNode': //
	              case 'OperatorNode:divide':
	                lhsTex = `\\left(${lhsTex}\\right)`;
	            }
	            break;
	          case 'OperatorNode:multiply':
	            if (this.implicit && implicit === 'hide') {
	              return lhsTex + '~' + rhsTex;
	            }
	        }
	        return lhsTex + op + rhsTex;
	      } else if (args.length > 2 && (this.getIdentifier() === 'OperatorNode:add' || this.getIdentifier() === 'OperatorNode:multiply')) {
	        const texifiedArgs = args.map(function (arg, index) {
	          arg = arg.toTex(options);
	          if (parens[index]) {
	            arg = `\\left(${arg}\\right)`;
	          }
	          return arg;
	        });
	        if (this.getIdentifier() === 'OperatorNode:multiply' && this.implicit && implicit === 'hide') {
	          return texifiedArgs.join('~');
	        }
	        return texifiedArgs.join(op);
	      } else {
	        // fall back to formatting as a function call
	        // as this is a fallback, it doesn't use
	        // fancy function names
	        return '\\mathrm{' + this.fn + '}\\left(' + args.map(function (arg) {
	          return arg.toTex(options);
	        }).join(',') + '\\right)';
	      }
	    }

	    /**
	     * Get identifier.
	     * @return {string}
	     */
	    getIdentifier() {
	      return this.type + ':' + this.fn;
	    }
	  }
	  (0, _defineProperty2.default)(OperatorNode, "name", name);
	  return OperatorNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return OperatorNode;
}

var ParenthesisNode = {};

var hasRequiredParenthesisNode;

function requireParenthesisNode () {
	if (hasRequiredParenthesisNode) return ParenthesisNode;
	hasRequiredParenthesisNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(ParenthesisNode, "__esModule", {
	  value: true
	});
	ParenthesisNode.createParenthesisNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'ParenthesisNode';
	const dependencies = ['Node'];
	ParenthesisNode.createParenthesisNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  class ParenthesisNode extends Node {
	    /**
	     * @constructor ParenthesisNode
	     * @extends {Node}
	     * A parenthesis node describes manual parenthesis from the user input
	     * @param {Node} content
	     * @extends {Node}
	     */
	    constructor(content) {
	      super();
	      // validate input
	      if (!(0, _is.isNode)(content)) {
	        throw new TypeError('Node expected for parameter "content"');
	      }
	      this.content = content;
	    }
	    get type() {
	      return name;
	    }
	    get isParenthesisNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      return this.content._compile(math, argNames);
	    }

	    /**
	     * Get the content of the current Node.
	     * @return {Node} content
	     * @override
	     **/
	    getContent() {
	      return this.content.getContent();
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.content, 'content', this);
	    }

	    /**
	     * Create a new ParenthesisNode whose child is the result of calling
	     * the provided callback function on the child of this node.
	     * @param {function(child: Node, path: string, parent: Node) : Node} callback
	     * @returns {ParenthesisNode} Returns a clone of the node
	     */
	    map(callback) {
	      const content = callback(this.content, 'content', this);
	      return new ParenthesisNode(content);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {ParenthesisNode}
	     */
	    clone() {
	      return new ParenthesisNode(this.content);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toString(options) {
	      if (!options || options && !options.parenthesis || options && options.parenthesis === 'keep') {
	        return '(' + this.content.toString(options) + ')';
	      }
	      return this.content.toString(options);
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        content: this.content
	      };
	    }

	    /**
	     * Instantiate an ParenthesisNode from its JSON representation
	     * @param {Object} json  An object structured like
	     *                       `{"mathjs": "ParenthesisNode", "content": ...}`,
	     *                       where mathjs is optional
	     * @returns {ParenthesisNode}
	     */
	    static fromJSON(json) {
	      return new ParenthesisNode(json.content);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toHTML(options) {
	      if (!options || options && !options.parenthesis || options && options.parenthesis === 'keep') {
	        return '<span class="math-parenthesis math-round-parenthesis">(</span>' + this.content.toHTML(options) + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      return this.content.toHTML(options);
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toTex(options) {
	      if (!options || options && !options.parenthesis || options && options.parenthesis === 'keep') {
	        return `\\left(${this.content.toTex(options)}\\right)`;
	      }
	      return this.content.toTex(options);
	    }
	  }
	  (0, _defineProperty2.default)(ParenthesisNode, "name", name);
	  return ParenthesisNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return ParenthesisNode;
}

var RangeNode = {};

var hasRequiredRangeNode;

function requireRangeNode () {
	if (hasRequiredRangeNode) return RangeNode;
	hasRequiredRangeNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(RangeNode, "__esModule", {
	  value: true
	});
	RangeNode.createRangeNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _operators = requireOperators();
	const name = 'RangeNode';
	const dependencies = ['Node'];
	RangeNode.createRangeNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  /**
	   * Calculate the necessary parentheses
	   * @param {Node} node
	   * @param {string} parenthesis
	   * @param {string} implicit
	   * @return {Object} parentheses
	   * @private
	   */
	  function calculateNecessaryParentheses(node, parenthesis, implicit) {
	    const precedence = (0, _operators.getPrecedence)(node, parenthesis, implicit);
	    const parens = {};
	    const startPrecedence = (0, _operators.getPrecedence)(node.start, parenthesis, implicit);
	    parens.start = startPrecedence !== null && startPrecedence <= precedence || parenthesis === 'all';
	    if (node.step) {
	      const stepPrecedence = (0, _operators.getPrecedence)(node.step, parenthesis, implicit);
	      parens.step = stepPrecedence !== null && stepPrecedence <= precedence || parenthesis === 'all';
	    }
	    const endPrecedence = (0, _operators.getPrecedence)(node.end, parenthesis, implicit);
	    parens.end = endPrecedence !== null && endPrecedence <= precedence || parenthesis === 'all';
	    return parens;
	  }
	  class RangeNode extends Node {
	    /**
	     * @constructor RangeNode
	     * @extends {Node}
	     * create a range
	     * @param {Node} start  included lower-bound
	     * @param {Node} end    included upper-bound
	     * @param {Node} [step] optional step
	     */
	    constructor(start, end, step) {
	      super();
	      // validate inputs
	      if (!(0, _is.isNode)(start)) throw new TypeError('Node expected');
	      if (!(0, _is.isNode)(end)) throw new TypeError('Node expected');
	      if (step && !(0, _is.isNode)(step)) throw new TypeError('Node expected');
	      if (arguments.length > 3) throw new Error('Too many arguments');
	      this.start = start; // included lower-bound
	      this.end = end; // included upper-bound
	      this.step = step || null; // optional step
	    }
	    get type() {
	      return name;
	    }
	    get isRangeNode() {
	      return true;
	    }

	    /**
	     * Check whether the RangeNode needs the `end` symbol to be defined.
	     * This end is the size of the Matrix in current dimension.
	     * @return {boolean}
	     */
	    needsEnd() {
	      // find all `end` symbols in this RangeNode
	      const endSymbols = this.filter(function (node) {
	        return (0, _is.isSymbolNode)(node) && node.name === 'end';
	      });
	      return endSymbols.length > 0;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const range = math.range;
	      const evalStart = this.start._compile(math, argNames);
	      const evalEnd = this.end._compile(math, argNames);
	      if (this.step) {
	        const evalStep = this.step._compile(math, argNames);
	        return function evalRangeNode(scope, args, context) {
	          return range(evalStart(scope, args, context), evalEnd(scope, args, context), evalStep(scope, args, context));
	        };
	      } else {
	        return function evalRangeNode(scope, args, context) {
	          return range(evalStart(scope, args, context), evalEnd(scope, args, context));
	        };
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.start, 'start', this);
	      callback(this.end, 'end', this);
	      if (this.step) {
	        callback(this.step, 'step', this);
	      }
	    }

	    /**
	     * Create a new RangeNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {RangeNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      return new RangeNode(this._ifNode(callback(this.start, 'start', this)), this._ifNode(callback(this.end, 'end', this)), this.step && this._ifNode(callback(this.step, 'step', this)));
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {RangeNode}
	     */
	    clone() {
	      return new RangeNode(this.start, this.end, this.step && this.step);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const parens = calculateNecessaryParentheses(this, parenthesis, options && options.implicit);

	      // format string as start:step:stop
	      let str;
	      let start = this.start.toString(options);
	      if (parens.start) {
	        start = '(' + start + ')';
	      }
	      str = start;
	      if (this.step) {
	        let step = this.step.toString(options);
	        if (parens.step) {
	          step = '(' + step + ')';
	        }
	        str += ':' + step;
	      }
	      let end = this.end.toString(options);
	      if (parens.end) {
	        end = '(' + end + ')';
	      }
	      str += ':' + end;
	      return str;
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        start: this.start,
	        end: this.end,
	        step: this.step
	      };
	    }

	    /**
	     * Instantiate an RangeNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     `{"mathjs": "RangeNode", "start": ..., "end": ..., "step": ...}`,
	     *     where mathjs is optional
	     * @returns {RangeNode}
	     */
	    static fromJSON(json) {
	      return new RangeNode(json.start, json.end, json.step);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const parens = calculateNecessaryParentheses(this, parenthesis, options && options.implicit);

	      // format string as start:step:stop
	      let str;
	      let start = this.start.toHTML(options);
	      if (parens.start) {
	        start = '<span class="math-parenthesis math-round-parenthesis">(</span>' + start + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      str = start;
	      if (this.step) {
	        let step = this.step.toHTML(options);
	        if (parens.step) {
	          step = '<span class="math-parenthesis math-round-parenthesis">(</span>' + step + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	        }
	        str += '<span class="math-operator math-range-operator">:</span>' + step;
	      }
	      let end = this.end.toHTML(options);
	      if (parens.end) {
	        end = '<span class="math-parenthesis math-round-parenthesis">(</span>' + end + '<span class="math-parenthesis math-round-parenthesis">)</span>';
	      }
	      str += '<span class="math-operator math-range-operator">:</span>' + end;
	      return str;
	    }

	    /**
	     * Get LaTeX representation
	     * @params {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const parens = calculateNecessaryParentheses(this, parenthesis, options && options.implicit);
	      let str = this.start.toTex(options);
	      if (parens.start) {
	        str = `\\left(${str}\\right)`;
	      }
	      if (this.step) {
	        let step = this.step.toTex(options);
	        if (parens.step) {
	          step = `\\left(${step}\\right)`;
	        }
	        str += ':' + step;
	      }
	      let end = this.end.toTex(options);
	      if (parens.end) {
	        end = `\\left(${end}\\right)`;
	      }
	      str += ':' + end;
	      return str;
	    }
	  }
	  (0, _defineProperty2.default)(RangeNode, "name", name);
	  return RangeNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return RangeNode;
}

var RelationalNode = {};

var hasRequiredRelationalNode;

function requireRelationalNode () {
	if (hasRequiredRelationalNode) return RelationalNode;
	hasRequiredRelationalNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(RelationalNode, "__esModule", {
	  value: true
	});
	RelationalNode.createRelationalNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _operators = requireOperators();
	var _string = requireString$2();
	var _customs = requireCustoms();
	var _latex = requireLatex();
	var _factory = requireFactory$b();
	const name = 'RelationalNode';
	const dependencies = ['Node'];
	RelationalNode.createRelationalNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Node
	  } = _ref;
	  const operatorMap = {
	    equal: '==',
	    unequal: '!=',
	    smaller: '<',
	    larger: '>',
	    smallerEq: '<=',
	    largerEq: '>='
	  };
	  class RelationalNode extends Node {
	    /**
	     * A node representing a chained conditional expression, such as 'x > y > z'
	     *
	     * @param {String[]} conditionals
	     *     An array of conditional operators used to compare the parameters
	     * @param {Node[]} params
	     *     The parameters that will be compared
	     *
	     * @constructor RelationalNode
	     * @extends {Node}
	     */
	    constructor(conditionals, params) {
	      super();
	      if (!Array.isArray(conditionals)) {
	        throw new TypeError('Parameter conditionals must be an array');
	      }
	      if (!Array.isArray(params)) {
	        throw new TypeError('Parameter params must be an array');
	      }
	      if (conditionals.length !== params.length - 1) {
	        throw new TypeError('Parameter params must contain exactly one more element ' + 'than parameter conditionals');
	      }
	      this.conditionals = conditionals;
	      this.params = params;
	    }
	    get type() {
	      return name;
	    }
	    get isRelationalNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const self = this;
	      const compiled = this.params.map(p => p._compile(math, argNames));
	      return function evalRelationalNode(scope, args, context) {
	        let evalLhs;
	        let evalRhs = compiled[0](scope, args, context);
	        for (let i = 0; i < self.conditionals.length; i++) {
	          evalLhs = evalRhs;
	          evalRhs = compiled[i + 1](scope, args, context);
	          const condFn = (0, _customs.getSafeProperty)(math, self.conditionals[i]);
	          if (!condFn(evalLhs, evalRhs)) {
	            return false;
	          }
	        }
	        return true;
	      };
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      this.params.forEach((n, i) => callback(n, 'params[' + i + ']', this), this);
	    }

	    /**
	     * Create a new RelationalNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {RelationalNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      return new RelationalNode(this.conditionals.slice(), this.params.map((n, i) => this._ifNode(callback(n, 'params[' + i + ']', this)), this));
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {RelationalNode}
	     */
	    clone() {
	      return new RelationalNode(this.conditionals, this.params);
	    }

	    /**
	     * Get string representation.
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const precedence = (0, _operators.getPrecedence)(this, parenthesis, options && options.implicit);
	      const paramStrings = this.params.map(function (p, index) {
	        const paramPrecedence = (0, _operators.getPrecedence)(p, parenthesis, options && options.implicit);
	        return parenthesis === 'all' || paramPrecedence !== null && paramPrecedence <= precedence ? '(' + p.toString(options) + ')' : p.toString(options);
	      });
	      let ret = paramStrings[0];
	      for (let i = 0; i < this.conditionals.length; i++) {
	        ret += ' ' + operatorMap[this.conditionals[i]];
	        ret += ' ' + paramStrings[i + 1];
	      }
	      return ret;
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        conditionals: this.conditionals,
	        params: this.params
	      };
	    }

	    /**
	     * Instantiate a RelationalNode from its JSON representation
	     * @param {Object} json
	     *     An object structured like
	     *     `{"mathjs": "RelationalNode", "conditionals": ..., "params": ...}`,
	     *     where mathjs is optional
	     * @returns {RelationalNode}
	     */
	    static fromJSON(json) {
	      return new RelationalNode(json.conditionals, json.params);
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const precedence = (0, _operators.getPrecedence)(this, parenthesis, options && options.implicit);
	      const paramStrings = this.params.map(function (p, index) {
	        const paramPrecedence = (0, _operators.getPrecedence)(p, parenthesis, options && options.implicit);
	        return parenthesis === 'all' || paramPrecedence !== null && paramPrecedence <= precedence ? '<span class="math-parenthesis math-round-parenthesis">(</span>' + p.toHTML(options) + '<span class="math-parenthesis math-round-parenthesis">)</span>' : p.toHTML(options);
	      });
	      let ret = paramStrings[0];
	      for (let i = 0; i < this.conditionals.length; i++) {
	        ret += '<span class="math-operator math-binary-operator ' + 'math-explicit-binary-operator">' + (0, _string.escape)(operatorMap[this.conditionals[i]]) + '</span>' + paramStrings[i + 1];
	      }
	      return ret;
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const parenthesis = options && options.parenthesis ? options.parenthesis : 'keep';
	      const precedence = (0, _operators.getPrecedence)(this, parenthesis, options && options.implicit);
	      const paramStrings = this.params.map(function (p, index) {
	        const paramPrecedence = (0, _operators.getPrecedence)(p, parenthesis, options && options.implicit);
	        return parenthesis === 'all' || paramPrecedence !== null && paramPrecedence <= precedence ? '\\left(' + p.toTex(options) + '\right)' : p.toTex(options);
	      });
	      let ret = paramStrings[0];
	      for (let i = 0; i < this.conditionals.length; i++) {
	        ret += _latex.latexOperators[this.conditionals[i]] + paramStrings[i + 1];
	      }
	      return ret;
	    }
	  }
	  (0, _defineProperty2.default)(RelationalNode, "name", name);
	  return RelationalNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return RelationalNode;
}

var SymbolNode = {};

var hasRequiredSymbolNode;

function requireSymbolNode () {
	if (hasRequiredSymbolNode) return SymbolNode;
	hasRequiredSymbolNode = 1;

	Object.defineProperty(SymbolNode, "__esModule", {
	  value: true
	});
	SymbolNode.createSymbolNode = void 0;
	var _string = requireString$2();
	var _customs = requireCustoms();
	var _factory = requireFactory$b();
	var _latex = requireLatex();
	const name = 'SymbolNode';
	const dependencies = ['math', '?Unit', 'Node'];
	SymbolNode.createSymbolNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    math,
	    Unit,
	    Node
	  } = _ref;
	  /**
	   * Check whether some name is a valueless unit like "inch".
	   * @param {string} name
	   * @return {boolean}
	   */
	  function isValuelessUnit(name) {
	    return Unit ? Unit.isValuelessUnit(name) : false;
	  }
	  class SymbolNode extends Node {
	    /**
	     * @constructor SymbolNode
	     * @extends {Node}
	     * A symbol node can hold and resolve a symbol
	     * @param {string} name
	     * @extends {Node}
	     */
	    constructor(name) {
	      super();
	      // validate input
	      if (typeof name !== 'string') {
	        throw new TypeError('String expected for parameter "name"');
	      }
	      this.name = name;
	    }
	    get type() {
	      return 'SymbolNode';
	    }
	    get isSymbolNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      const name = this.name;
	      if (argNames[name] === true) {
	        // this is a FunctionAssignment argument
	        // (like an x when inside the expression of a function
	        // assignment `f(x) = ...`)
	        return function (scope, args, context) {
	          return (0, _customs.getSafeProperty)(args, name);
	        };
	      } else if (name in math) {
	        return function (scope, args, context) {
	          return scope.has(name) ? scope.get(name) : (0, _customs.getSafeProperty)(math, name);
	        };
	      } else {
	        const isUnit = isValuelessUnit(name);
	        return function (scope, args, context) {
	          return scope.has(name) ? scope.get(name) : isUnit ? new Unit(null, name) : SymbolNode.onUndefinedSymbol(name);
	        };
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      // nothing to do, we don't have any children
	    }

	    /**
	     * Create a new SymbolNode with children produced by the given callback.
	     * Trivial since a SymbolNode has no children
	     * @param {function(child: Node, path: string, parent: Node) : Node} callback
	     * @returns {SymbolNode} Returns a clone of the node
	     */
	    map(callback) {
	      return this.clone();
	    }

	    /**
	     * Throws an error 'Undefined symbol {name}'
	     * @param {string} name
	     */
	    static onUndefinedSymbol(name) {
	      throw new Error('Undefined symbol ' + name);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {SymbolNode}
	     */
	    clone() {
	      return new SymbolNode(this.name);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toString(options) {
	      return this.name;
	    }

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toHTML(options) {
	      const name = (0, _string.escape)(this.name);
	      if (name === 'true' || name === 'false') {
	        return '<span class="math-symbol math-boolean">' + name + '</span>';
	      } else if (name === 'i') {
	        return '<span class="math-symbol math-imaginary-symbol">' + name + '</span>';
	      } else if (name === 'Infinity') {
	        return '<span class="math-symbol math-infinity-symbol">' + name + '</span>';
	      } else if (name === 'NaN') {
	        return '<span class="math-symbol math-nan-symbol">' + name + '</span>';
	      } else if (name === 'null') {
	        return '<span class="math-symbol math-null-symbol">' + name + '</span>';
	      } else if (name === 'undefined') {
	        return '<span class="math-symbol math-undefined-symbol">' + name + '</span>';
	      }
	      return '<span class="math-symbol">' + name + '</span>';
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: 'SymbolNode',
	        name: this.name
	      };
	    }

	    /**
	     * Instantiate a SymbolNode from its JSON representation
	     * @param {Object} json  An object structured like
	     *                       `{"mathjs": "SymbolNode", name: "x"}`,
	     *                       where mathjs is optional
	     * @returns {SymbolNode}
	     */
	    static fromJSON(json) {
	      return new SymbolNode(json.name);
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    _toTex(options) {
	      let isUnit = false;
	      if (typeof math[this.name] === 'undefined' && isValuelessUnit(this.name)) {
	        isUnit = true;
	      }
	      const symbol = (0, _latex.toSymbol)(this.name, isUnit);
	      if (symbol[0] === '\\') {
	        // no space needed if the symbol starts with '\'
	        return symbol;
	      }
	      // the space prevents symbols from breaking stuff like '\cdot'
	      // if it's written right before the symbol
	      return ' ' + symbol;
	    }
	  }
	  return SymbolNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return SymbolNode;
}

var FunctionNode = {};

var hasRequiredFunctionNode;

function requireFunctionNode () {
	if (hasRequiredFunctionNode) return FunctionNode;
	hasRequiredFunctionNode = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(FunctionNode, "__esModule", {
	  value: true
	});
	FunctionNode.createFunctionNode = void 0;
	var _defineProperty2 = _interopRequireDefault(requireDefineProperty());
	var _is = requireIs();
	var _string = requireString$2();
	var _object = requireObject$3();
	var _customs = requireCustoms();
	var _scope = requireScope();
	var _factory = requireFactory$b();
	var _latex = requireLatex();
	const name = 'FunctionNode';
	const dependencies = ['math', 'Node', 'SymbolNode'];
	FunctionNode.createFunctionNode = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  var _FunctionNode;
	  let {
	    math,
	    Node,
	    SymbolNode
	  } = _ref;
	  /* format to fixed length */
	  const strin = entity => (0, _string.format)(entity, {
	    truncate: 78
	  });

	  /*
	   * Expand a LaTeX template
	   *
	   * @param {string} template
	   * @param {Node} node
	   * @param {Object} options
	   * @private
	   **/
	  function expandTemplate(template, node, options) {
	    let latex = '';

	    // Match everything of the form ${identifier} or ${identifier[2]} or $$
	    // while submatching identifier and 2 (in the second case)
	    const regex = /\$(?:\{([a-z_][a-z_0-9]*)(?:\[([0-9]+)\])?\}|\$)/gi;
	    let inputPos = 0; // position in the input string
	    let match;
	    while ((match = regex.exec(template)) !== null) {
	      // go through all matches
	      // add everything in front of the match to the LaTeX string
	      latex += template.substring(inputPos, match.index);
	      inputPos = match.index;
	      if (match[0] === '$$') {
	        // escaped dollar sign
	        latex += '$';
	        inputPos++;
	      } else {
	        // template parameter
	        inputPos += match[0].length;
	        const property = node[match[1]];
	        if (!property) {
	          throw new ReferenceError('Template: Property ' + match[1] + ' does not exist.');
	        }
	        if (match[2] === undefined) {
	          // no square brackets
	          switch (typeof property) {
	            case 'string':
	              latex += property;
	              break;
	            case 'object':
	              if ((0, _is.isNode)(property)) {
	                latex += property.toTex(options);
	              } else if (Array.isArray(property)) {
	                // make array of Nodes into comma separated list
	                latex += property.map(function (arg, index) {
	                  if ((0, _is.isNode)(arg)) {
	                    return arg.toTex(options);
	                  }
	                  throw new TypeError('Template: ' + match[1] + '[' + index + '] is not a Node.');
	                }).join(',');
	              } else {
	                throw new TypeError('Template: ' + match[1] + ' has to be a Node, String or array of Nodes');
	              }
	              break;
	            default:
	              throw new TypeError('Template: ' + match[1] + ' has to be a Node, String or array of Nodes');
	          }
	        } else {
	          // with square brackets
	          if ((0, _is.isNode)(property[match[2]] && property[match[2]])) {
	            latex += property[match[2]].toTex(options);
	          } else {
	            throw new TypeError('Template: ' + match[1] + '[' + match[2] + '] is not a Node.');
	          }
	        }
	      }
	    }
	    latex += template.slice(inputPos); // append rest of the template

	    return latex;
	  }
	  class FunctionNode extends Node {
	    /**
	     * @constructor FunctionNode
	     * @extends {./Node}
	     * invoke a list with arguments on a node
	     * @param {./Node | string} fn
	     *     Item resolving to a function on which to invoke
	     *     the arguments, typically a SymbolNode or AccessorNode
	     * @param {./Node[]} args
	     */
	    constructor(fn, args, optional) {
	      super();
	      if (typeof fn === 'string') {
	        fn = new SymbolNode(fn);
	      }

	      // validate input
	      if (!(0, _is.isNode)(fn)) throw new TypeError('Node expected as parameter "fn"');
	      if (!Array.isArray(args) || !args.every(_is.isNode)) {
	        throw new TypeError('Array containing Nodes expected for parameter "args"');
	      }
	      const optionalType = typeof optional;
	      if (!(optionalType === 'undefined' || optionalType === 'boolean')) {
	        throw new TypeError('optional flag, if specified, must be boolean');
	      }
	      this.fn = fn;
	      this.args = args || [];
	      this.optional = !!optional;
	    }

	    // readonly property name
	    get name() {
	      return this.fn.name || '';
	    }
	    get type() {
	      return name;
	    }
	    get isFunctionNode() {
	      return true;
	    }

	    /**
	     * Compile a node into a JavaScript function.
	     * This basically pre-calculates as much as possible and only leaves open
	     * calculations which depend on a dynamic scope with variables.
	     * @param {Object} math     Math.js namespace with functions and constants.
	     * @param {Object} argNames An object with argument names as key and `true`
	     *                          as value. Used in the SymbolNode to optimize
	     *                          for arguments from user assigned functions
	     *                          (see FunctionAssignmentNode) or special symbols
	     *                          like `end` (see IndexNode).
	     * @return {function} Returns a function which can be called like:
	     *                        evalNode(scope: Object, args: Object, context: *)
	     */
	    _compile(math, argNames) {
	      // compile arguments
	      const evalArgs = this.args.map(arg => arg._compile(math, argNames));
	      const fromOptionalChaining = this.optional || (0, _is.isAccessorNode)(this.fn) && this.fn.optionalChaining;
	      if ((0, _is.isSymbolNode)(this.fn)) {
	        const name = this.fn.name;
	        if (!argNames[name]) {
	          // we can statically determine whether the function
	          // has the rawArgs property
	          const fn = name in math ? (0, _customs.getSafeProperty)(math, name) : undefined;
	          const isRaw = typeof fn === 'function' && fn.rawArgs === true;
	          const resolveFn = scope => {
	            let value;
	            if (scope.has(name)) {
	              value = scope.get(name);
	            } else if (name in math) {
	              value = (0, _customs.getSafeProperty)(math, name);
	            } else if (fromOptionalChaining) value = undefined;else return FunctionNode.onUndefinedFunction(name);
	            if (typeof value === 'function' || fromOptionalChaining && value === undefined) {
	              return value;
	            }
	            throw new TypeError(`'${name}' is not a function; its value is:\n  ${strin(value)}`);
	          };
	          if (isRaw) {
	            // pass unevaluated parameters (nodes) to the function
	            // "raw" evaluation
	            const rawArgs = this.args;
	            return function evalFunctionNode(scope, args, context) {
	              const fn = resolveFn(scope);

	              // the original function can be overwritten in the scope with a non-rawArgs function
	              if (fn.rawArgs === true) {
	                return fn(rawArgs, math, (0, _scope.createSubScope)(scope, args));
	              } else {
	                // "regular" evaluation
	                const values = evalArgs.map(evalArg => evalArg(scope, args, context));
	                return fn(...values);
	              }
	            };
	          } else {
	            // "regular" evaluation
	            switch (evalArgs.length) {
	              case 0:
	                return function evalFunctionNode(scope, args, context) {
	                  const fn = resolveFn(scope);
	                  if (fromOptionalChaining && fn === undefined) return undefined;
	                  return fn();
	                };
	              case 1:
	                return function evalFunctionNode(scope, args, context) {
	                  const fn = resolveFn(scope);
	                  if (fromOptionalChaining && fn === undefined) return undefined;
	                  const evalArg0 = evalArgs[0];
	                  return fn(evalArg0(scope, args, context));
	                };
	              case 2:
	                return function evalFunctionNode(scope, args, context) {
	                  const fn = resolveFn(scope);
	                  if (fromOptionalChaining && fn === undefined) return undefined;
	                  const evalArg0 = evalArgs[0];
	                  const evalArg1 = evalArgs[1];
	                  return fn(evalArg0(scope, args, context), evalArg1(scope, args, context));
	                };
	              default:
	                return function evalFunctionNode(scope, args, context) {
	                  const fn = resolveFn(scope);
	                  if (fromOptionalChaining && fn === undefined) return undefined;
	                  const values = evalArgs.map(evalArg => evalArg(scope, args, context));
	                  return fn(...values);
	                };
	            }
	          }
	        } else {
	          // the function symbol is an argName
	          const rawArgs = this.args;
	          return function evalFunctionNode(scope, args, context) {
	            const fn = (0, _customs.getSafeProperty)(args, name);
	            if (fromOptionalChaining && fn === undefined) return undefined;
	            if (typeof fn !== 'function') {
	              throw new TypeError(`Argument '${name}' was not a function; received: ${strin(fn)}`);
	            }
	            if (fn.rawArgs) {
	              // "Raw" evaluation
	              return fn(rawArgs, math, (0, _scope.createSubScope)(scope, args));
	            } else {
	              const values = evalArgs.map(evalArg => evalArg(scope, args, context));
	              return fn.apply(fn, values);
	            }
	          };
	        }
	      } else if ((0, _is.isAccessorNode)(this.fn) && (0, _is.isIndexNode)(this.fn.index) && this.fn.index.isObjectProperty()) {
	        // execute the function with the right context:
	        // the object of the AccessorNode

	        const evalObject = this.fn.object._compile(math, argNames);
	        const prop = this.fn.index.getObjectProperty();
	        const rawArgs = this.args;
	        return function evalFunctionNode(scope, args, context) {
	          const object = evalObject(scope, args, context);

	          // Optional chaining: if the base object is nullish, short-circuit to undefined
	          if (fromOptionalChaining && (object == null || object[prop] === undefined)) {
	            return undefined;
	          }
	          const fn = (0, _customs.getSafeMethod)(object, prop);
	          if (fn !== null && fn !== void 0 && fn.rawArgs) {
	            // "Raw" evaluation
	            return fn(rawArgs, math, (0, _scope.createSubScope)(scope, args));
	          } else {
	            // "regular" evaluation
	            const values = evalArgs.map(evalArg => evalArg(scope, args, context));
	            return fn.apply(object, values);
	          }
	        };
	      } else {
	        // node.fn.isAccessorNode && !node.fn.index.isObjectProperty()
	        // we have to dynamically determine whether the function has the
	        // rawArgs property
	        const fnExpr = this.fn.toString();
	        const evalFn = this.fn._compile(math, argNames);
	        const rawArgs = this.args;
	        return function evalFunctionNode(scope, args, context) {
	          const fn = evalFn(scope, args, context);
	          if (fromOptionalChaining && fn === undefined) return undefined;
	          if (typeof fn !== 'function') {
	            throw new TypeError(`Expression '${fnExpr}' did not evaluate to a function; value is:` + `\n  ${strin(fn)}`);
	          }
	          if (fn.rawArgs) {
	            // "Raw" evaluation
	            return fn(rawArgs, math, (0, _scope.createSubScope)(scope, args));
	          } else {
	            // "regular" evaluation
	            const values = evalArgs.map(evalArg => evalArg(scope, args, context));
	            return fn.apply(fn, values);
	          }
	        };
	      }
	    }

	    /**
	     * Execute a callback for each of the child nodes of this node
	     * @param {function(child: Node, path: string, parent: Node)} callback
	     */
	    forEach(callback) {
	      callback(this.fn, 'fn', this);
	      for (let i = 0; i < this.args.length; i++) {
	        callback(this.args[i], 'args[' + i + ']', this);
	      }
	    }

	    /**
	     * Create a new FunctionNode whose children are the results of calling
	     * the provided callback function for each child of the original node.
	     * @param {function(child: Node, path: string, parent: Node): Node} callback
	     * @returns {FunctionNode} Returns a transformed copy of the node
	     */
	    map(callback) {
	      const fn = this._ifNode(callback(this.fn, 'fn', this));
	      const args = [];
	      for (let i = 0; i < this.args.length; i++) {
	        args[i] = this._ifNode(callback(this.args[i], 'args[' + i + ']', this));
	      }
	      return new FunctionNode(fn, args);
	    }

	    /**
	     * Create a clone of this node, a shallow copy
	     * @return {FunctionNode}
	     */
	    clone() {
	      return new FunctionNode(this.fn, this.args.slice(0));
	    }

	    /**
	     * Throws an error 'Undefined function {name}'
	     * @param {string} name
	     */

	    /**
	     * Get string representation. (wrapper function)
	     * This overrides parts of Node's toString function.
	     * If callback is an object containing callbacks, it
	     * calls the correct callback for the current node,
	     * otherwise it falls back to calling Node's toString
	     * function.
	     *
	     * @param {Object} options
	     * @return {string} str
	     * @override
	     */
	    toString(options) {
	      let customString;
	      const name = this.fn.toString(options);
	      if (options && typeof options.handler === 'object' && (0, _object.hasOwnProperty)(options.handler, name)) {
	        // callback is a map of callback functions
	        customString = options.handler[name](this, options);
	      }
	      if (typeof customString !== 'undefined') {
	        return customString;
	      }

	      // fall back to Node's toString
	      return super.toString(options);
	    }

	    /**
	     * Get string representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toString(options) {
	      const args = this.args.map(function (arg) {
	        return arg.toString(options);
	      });
	      const fn = (0, _is.isFunctionAssignmentNode)(this.fn) ? '(' + this.fn.toString(options) + ')' : this.fn.toString(options);

	      // format the arguments like "add(2, 4.2)"
	      return fn + '(' + args.join(', ') + ')';
	    }

	    /**
	     * Get a JSON representation of the node
	     * @returns {Object}
	     */
	    toJSON() {
	      return {
	        mathjs: name,
	        fn: this.fn,
	        args: this.args
	      };
	    }

	    /**
	     * Instantiate an AssignmentNode from its JSON representation
	     * @param {Object} json  An object structured like
	     *                       `{"mathjs": "FunctionNode", fn: ..., args: ...}`,
	     *                       where mathjs is optional
	     * @returns {FunctionNode}
	     */

	    /**
	     * Get HTML representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toHTML(options) {
	      const args = this.args.map(function (arg) {
	        return arg.toHTML(options);
	      });

	      // format the arguments like "add(2, 4.2)"
	      return '<span class="math-function">' + (0, _string.escape)(this.fn) + '</span><span class="math-paranthesis math-round-parenthesis">(</span>' + args.join('<span class="math-separator">,</span>') + '<span class="math-paranthesis math-round-parenthesis">)</span>';
	    }

	    /**
	     * Get LaTeX representation. (wrapper function)
	     * This overrides parts of Node's toTex function.
	     * If callback is an object containing callbacks, it
	     * calls the correct callback for the current node,
	     * otherwise it falls back to calling Node's toTex
	     * function.
	     *
	     * @param {Object} options
	     * @return {string}
	     */
	    toTex(options) {
	      let customTex;
	      if (options && typeof options.handler === 'object' && (0, _object.hasOwnProperty)(options.handler, this.name)) {
	        // callback is a map of callback functions
	        customTex = options.handler[this.name](this, options);
	      }
	      if (typeof customTex !== 'undefined') {
	        return customTex;
	      }

	      // fall back to Node's toTex
	      return super.toTex(options);
	    }

	    /**
	     * Get LaTeX representation
	     * @param {Object} options
	     * @return {string} str
	     */
	    _toTex(options) {
	      const args = this.args.map(function (arg) {
	        // get LaTeX of the arguments
	        return arg.toTex(options);
	      });
	      let latexConverter;
	      if (_latex.latexFunctions[this.name]) {
	        latexConverter = _latex.latexFunctions[this.name];
	      }

	      // toTex property on the function itself
	      if (math[this.name] && (typeof math[this.name].toTex === 'function' || typeof math[this.name].toTex === 'object' || typeof math[this.name].toTex === 'string')) {
	        // .toTex is a callback function
	        latexConverter = math[this.name].toTex;
	      }
	      let customToTex;
	      switch (typeof latexConverter) {
	        case 'function':
	          // a callback function
	          customToTex = latexConverter(this, options);
	          break;
	        case 'string':
	          // a template string
	          customToTex = expandTemplate(latexConverter, this, options);
	          break;
	        case 'object':
	          // an object with different "converters" for different
	          // numbers of arguments
	          switch (typeof latexConverter[args.length]) {
	            case 'function':
	              customToTex = latexConverter[args.length](this, options);
	              break;
	            case 'string':
	              customToTex = expandTemplate(latexConverter[args.length], this, options);
	              break;
	          }
	      }
	      if (typeof customToTex !== 'undefined') {
	        return customToTex;
	      }
	      return expandTemplate(_latex.defaultTemplate, this, options);
	    }

	    /**
	     * Get identifier.
	     * @return {string}
	     */
	    getIdentifier() {
	      return this.type + ':' + this.name;
	    }
	  }
	  _FunctionNode = FunctionNode;
	  (0, _defineProperty2.default)(FunctionNode, "name", name);
	  (0, _defineProperty2.default)(FunctionNode, "onUndefinedFunction", function (name) {
	    throw new Error('Undefined function ' + name);
	  });
	  (0, _defineProperty2.default)(FunctionNode, "fromJSON", function (json) {
	    return new _FunctionNode(json.fn, json.args);
	  });
	  return FunctionNode;
	}, {
	  isClass: true,
	  isNode: true
	});
	return FunctionNode;
}

var parse$1 = {};

var hasRequiredParse$2;

function requireParse$2 () {
	if (hasRequiredParse$2) return parse$1;
	hasRequiredParse$2 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(parse$1, "__esModule", {
	  value: true
	});
	parse$1.createParse = void 0;
	var _extends2 = _interopRequireDefault(require_extends());
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _collection = requireCollection();
	var _number = requireNumber$3();
	var _object = requireObject$3();
	const name = 'parse';
	const dependencies = ['typed', 'numeric', 'config', 'AccessorNode', 'ArrayNode', 'AssignmentNode', 'BlockNode', 'ConditionalNode', 'ConstantNode', 'FunctionAssignmentNode', 'FunctionNode', 'IndexNode', 'ObjectNode', 'OperatorNode', 'ParenthesisNode', 'RangeNode', 'RelationalNode', 'SymbolNode'];
	parse$1.createParse = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    numeric,
	    config,
	    AccessorNode,
	    ArrayNode,
	    AssignmentNode,
	    BlockNode,
	    ConditionalNode,
	    ConstantNode,
	    FunctionAssignmentNode,
	    FunctionNode,
	    IndexNode,
	    ObjectNode,
	    OperatorNode,
	    ParenthesisNode,
	    RangeNode,
	    RelationalNode,
	    SymbolNode
	  } = _ref;
	  /**
	   * Parse an expression. Returns a node tree, which can be evaluated by
	   * invoking node.evaluate() or transformed into a functional object via node.compile().
	   *
	   * Note the evaluating arbitrary expressions may involve security risks,
	   * see [https://mathjs.org/docs/expressions/security.html](https://mathjs.org/docs/expressions/security.html) for more information.
	   *
	   * Syntax:
	   *
	   *     math.parse(expr)
	   *     math.parse(expr, options)
	   *     math.parse([expr1, expr2, expr3, ...])
	   *     math.parse([expr1, expr2, expr3, ...], options)
	   *
	   * Example:
	   *
	   *     const node1 = math.parse('sqrt(3^2 + 4^2)')
	   *     node1.compile().evaluate() // 5
	   *
	   *     let scope = {a:3, b:4}
	   *     const node2 = math.parse('a * b')
	   *     node2.evaluate(scope) // 12
	   *     const code2 = node2.compile()
	   *     code2.evaluate(scope) // 12
	   *     scope.a = 5
	   *     code2.evaluate(scope) // 20
	   *
	   *     const nodes = math.parse(['a = 3', 'b = 4', 'a * b'])
	   *     nodes[2].compile().evaluate() // 12
	   *
	   * See also:
	   *
	   *     evaluate, compile
	   *
	   * History:
	   *
	   *     v0.9   Created
	   *     v0.13  Switched to one-based indices
	   *     v0.14  Added `[1,2;3,4]` notation for matrices
	   *     v0.18  Dropped the `function` keyword
	   *     v0.20  Added ternary conditional
	   *     v0.27  Allow multi-line expressions; allow functions that receive
	   *            unevaluated parameters (`rawArgs`)
	   *     v3     Add object notation; allow assignments internal to other
	   *            expressions
	   *     v7.3   Supported binary, octal, and hexadecimal notation
	   *     v9.5   Support for calculations with percentages
	   *     v12.4  Allow trailing commas in matrices
	   *     v14.8  Add nullish coalescing operator
	   *     v15.1  Add optional chaining operator
	   *
	   * @param {string | string[] | Matrix} expr          Expression to be parsed
	   * @param {{nodes: Object<string, Node>}} [options]  Available options:
	   *                                                   - `nodes` a set of custom nodes
	   * @return {Node | Node[]} node
	   * @throws {Error}
	   */
	  const parse = typed(name, {
	    string: function (expression) {
	      return parseStart(expression, {});
	    },
	    'Array | Matrix': function (expressions) {
	      return parseMultiple(expressions, {});
	    },
	    'string, Object': function (expression, options) {
	      const extraNodes = options.nodes !== undefined ? options.nodes : {};
	      return parseStart(expression, extraNodes);
	    },
	    'Array | Matrix, Object': parseMultiple
	  });
	  function parseMultiple(expressions) {
	    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	    const extraNodes = options.nodes !== undefined ? options.nodes : {};

	    // parse an array or matrix with expressions
	    return (0, _collection.deepMap)(expressions, function (elem) {
	      if (typeof elem !== 'string') throw new TypeError('String expected');
	      return parseStart(elem, extraNodes);
	    });
	  }

	  // token types enumeration
	  const TOKENTYPE = {
	    NULL: 0,
	    DELIMITER: 1,
	    NUMBER: 2,
	    SYMBOL: 3,
	    UNKNOWN: 4
	  };

	  // map with all delimiters
	  const DELIMITERS = {
	    ',': true,
	    '(': true,
	    ')': true,
	    '[': true,
	    ']': true,
	    '{': true,
	    '}': true,
	    '"': true,
	    '\'': true,
	    ';': true,
	    '+': true,
	    '-': true,
	    '*': true,
	    '.*': true,
	    '/': true,
	    './': true,
	    '%': true,
	    '^': true,
	    '.^': true,
	    '~': true,
	    '!': true,
	    '&': true,
	    '|': true,
	    '^|': true,
	    '=': true,
	    ':': true,
	    '?': true,
	    '?.': true,
	    '??': true,
	    '==': true,
	    '!=': true,
	    '<': true,
	    '>': true,
	    '<=': true,
	    '>=': true,
	    '<<': true,
	    '>>': true,
	    '>>>': true
	  };

	  // map with all named delimiters
	  const NAMED_DELIMITERS = {
	    mod: true,
	    to: true,
	    in: true,
	    and: true,
	    xor: true,
	    or: true,
	    not: true
	  };
	  const CONSTANTS = {
	    true: true,
	    false: false,
	    null: null,
	    undefined
	  };
	  const NUMERIC_CONSTANTS = ['NaN', 'Infinity'];
	  const ESCAPE_CHARACTERS = {
	    '"': '"',
	    "'": "'",
	    '\\': '\\',
	    '/': '/',
	    b: '\b',
	    f: '\f',
	    n: '\n',
	    r: '\r',
	    t: '\t'
	    // note that \u is handled separately in parseStringToken()
	  };
	  function initialState() {
	    return {
	      extraNodes: {},
	      // current extra nodes, must be careful not to mutate
	      expression: '',
	      // current expression
	      comment: '',
	      // last parsed comment
	      index: 0,
	      // current index in expr
	      token: '',
	      // current token
	      tokenType: TOKENTYPE.NULL,
	      // type of the token
	      nestingLevel: 0,
	      // level of nesting inside parameters, used to ignore newline characters
	      conditionalLevel: null // when a conditional is being parsed, the level of the conditional is stored here
	    };
	  }

	  /**
	   * View upto `length` characters of the expression starting at the current character.
	   *
	   * @param {Object} state
	   * @param {number} [length=1] Number of characters to view
	   * @returns {string}
	   * @private
	   */
	  function currentString(state, length) {
	    return state.expression.substr(state.index, length);
	  }

	  /**
	   * View the current character. Returns '' if end of expression is reached.
	   *
	   * @param {Object} state
	   * @returns {string}
	   * @private
	   */
	  function currentCharacter(state) {
	    return currentString(state, 1);
	  }

	  /**
	   * Get the next character from the expression.
	   * The character is stored into the char c. If the end of the expression is
	   * reached, the function puts an empty string in c.
	   * @private
	   */
	  function next(state) {
	    state.index++;
	  }

	  /**
	   * Preview the previous character from the expression.
	   * @return {string} cNext
	   * @private
	   */
	  function prevCharacter(state) {
	    return state.expression.charAt(state.index - 1);
	  }

	  /**
	   * Preview the next character from the expression.
	   * @return {string} cNext
	   * @private
	   */
	  function nextCharacter(state) {
	    return state.expression.charAt(state.index + 1);
	  }

	  /**
	   * Get next token in the current string expr.
	   * The token and token type are available as token and tokenType
	   * @private
	   */
	  function getToken(state) {
	    state.tokenType = TOKENTYPE.NULL;
	    state.token = '';
	    state.comment = '';

	    // skip over ignored characters:
	    while (true) {
	      // comments:
	      if (currentCharacter(state) === '#') {
	        while (currentCharacter(state) !== '\n' && currentCharacter(state) !== '') {
	          state.comment += currentCharacter(state);
	          next(state);
	        }
	      }
	      // whitespace: space, tab, and newline when inside parameters
	      if (parse.isWhitespace(currentCharacter(state), state.nestingLevel)) {
	        next(state);
	      } else {
	        break;
	      }
	    }

	    // check for end of expression
	    if (currentCharacter(state) === '') {
	      // token is still empty
	      state.tokenType = TOKENTYPE.DELIMITER;
	      return;
	    }

	    // check for new line character
	    if (currentCharacter(state) === '\n' && !state.nestingLevel) {
	      state.tokenType = TOKENTYPE.DELIMITER;
	      state.token = currentCharacter(state);
	      next(state);
	      return;
	    }
	    const c1 = currentCharacter(state);
	    const c2 = currentString(state, 2);
	    const c3 = currentString(state, 3);
	    if (c3.length === 3 && DELIMITERS[c3]) {
	      state.tokenType = TOKENTYPE.DELIMITER;
	      state.token = c3;
	      next(state);
	      next(state);
	      next(state);
	      return;
	    }

	    // check for delimiters consisting of 2 characters
	    // Special case: the check for '?.' is to prevent a case like 'a?.3:.7' from being interpreted as optional chaining
	    // TODO: refactor the tokenization into some better way to deal with cases like 'a?.3:.7', see https://github.com/josdejong/mathjs/pull/3584
	    if (c2.length === 2 && DELIMITERS[c2] && (c2 !== '?.' || !parse.isDigit(state.expression.charAt(state.index + 2)))) {
	      state.tokenType = TOKENTYPE.DELIMITER;
	      state.token = c2;
	      next(state);
	      next(state);
	      return;
	    }

	    // check for delimiters consisting of 1 character
	    if (DELIMITERS[c1]) {
	      state.tokenType = TOKENTYPE.DELIMITER;
	      state.token = c1;
	      next(state);
	      return;
	    }

	    // check for a number
	    if (parse.isDigitDot(c1)) {
	      state.tokenType = TOKENTYPE.NUMBER;

	      // check for binary, octal, or hex
	      const c2 = currentString(state, 2);
	      if (c2 === '0b' || c2 === '0o' || c2 === '0x') {
	        state.token += currentCharacter(state);
	        next(state);
	        state.token += currentCharacter(state);
	        next(state);
	        while (parse.isAlpha(currentCharacter(state), prevCharacter(state), nextCharacter(state)) || parse.isDigit(currentCharacter(state))) {
	          state.token += currentCharacter(state);
	          next(state);
	        }
	        if (currentCharacter(state) === '.') {
	          // this number has a radix point
	          state.token += '.';
	          next(state);
	          // get the digits after the radix
	          while (parse.isAlpha(currentCharacter(state), prevCharacter(state), nextCharacter(state)) || parse.isDigit(currentCharacter(state))) {
	            state.token += currentCharacter(state);
	            next(state);
	          }
	        } else if (currentCharacter(state) === 'i') {
	          // this number has a word size suffix
	          state.token += 'i';
	          next(state);
	          // get the word size
	          while (parse.isDigit(currentCharacter(state))) {
	            state.token += currentCharacter(state);
	            next(state);
	          }
	        }
	        return;
	      }

	      // get number, can have a single dot
	      if (currentCharacter(state) === '.') {
	        state.token += currentCharacter(state);
	        next(state);
	        if (!parse.isDigit(currentCharacter(state))) {
	          // this is no number, it is just a dot (can be dot notation)
	          state.tokenType = TOKENTYPE.DELIMITER;
	          return;
	        }
	      } else {
	        while (parse.isDigit(currentCharacter(state))) {
	          state.token += currentCharacter(state);
	          next(state);
	        }
	        if (parse.isDecimalMark(currentCharacter(state), nextCharacter(state))) {
	          state.token += currentCharacter(state);
	          next(state);
	        }
	      }
	      while (parse.isDigit(currentCharacter(state))) {
	        state.token += currentCharacter(state);
	        next(state);
	      }
	      // check for exponential notation like "2.3e-4", "1.23e50" or "2e+4"
	      if (currentCharacter(state) === 'E' || currentCharacter(state) === 'e') {
	        if (parse.isDigit(nextCharacter(state)) || nextCharacter(state) === '-' || nextCharacter(state) === '+') {
	          state.token += currentCharacter(state);
	          next(state);
	          if (currentCharacter(state) === '+' || currentCharacter(state) === '-') {
	            state.token += currentCharacter(state);
	            next(state);
	          }
	          // Scientific notation MUST be followed by an exponent
	          if (!parse.isDigit(currentCharacter(state))) {
	            throw createSyntaxError(state, 'Digit expected, got "' + currentCharacter(state) + '"');
	          }
	          while (parse.isDigit(currentCharacter(state))) {
	            state.token += currentCharacter(state);
	            next(state);
	          }
	          if (parse.isDecimalMark(currentCharacter(state), nextCharacter(state))) {
	            throw createSyntaxError(state, 'Digit expected, got "' + currentCharacter(state) + '"');
	          }
	        } else if (parse.isDecimalMark(nextCharacter(state), state.expression.charAt(state.index + 2))) {
	          next(state);
	          throw createSyntaxError(state, 'Digit expected, got "' + currentCharacter(state) + '"');
	        }
	      }
	      return;
	    }

	    // check for variables, functions, named operators
	    if (parse.isAlpha(currentCharacter(state), prevCharacter(state), nextCharacter(state))) {
	      while (parse.isAlpha(currentCharacter(state), prevCharacter(state), nextCharacter(state)) || parse.isDigit(currentCharacter(state))) {
	        state.token += currentCharacter(state);
	        next(state);
	      }
	      if ((0, _object.hasOwnProperty)(NAMED_DELIMITERS, state.token)) {
	        state.tokenType = TOKENTYPE.DELIMITER;
	      } else {
	        state.tokenType = TOKENTYPE.SYMBOL;
	      }
	      return;
	    }

	    // something unknown is found, wrong characters -> a syntax error
	    state.tokenType = TOKENTYPE.UNKNOWN;
	    while (currentCharacter(state) !== '') {
	      state.token += currentCharacter(state);
	      next(state);
	    }
	    throw createSyntaxError(state, 'Syntax error in part "' + state.token + '"');
	  }

	  /**
	   * Get next token and skip newline tokens
	   */
	  function getTokenSkipNewline(state) {
	    do {
	      getToken(state);
	    } while (state.token === '\n'); // eslint-disable-line no-unmodified-loop-condition
	  }

	  /**
	   * Open parameters.
	   * New line characters will be ignored until closeParams(state) is called
	   */
	  function openParams(state) {
	    state.nestingLevel++;
	  }

	  /**
	   * Close parameters.
	   * New line characters will no longer be ignored
	   */
	  function closeParams(state) {
	    state.nestingLevel--;
	  }

	  /**
	   * Checks whether the current character `c` is a valid alpha character:
	   *
	   * - A latin letter (upper or lower case) Ascii: a-z, A-Z
	   * - An underscore                        Ascii: _
	   * - A dollar sign                        Ascii: $
	   * - A latin letter with accents          Unicode: \u00C0 - \u02AF
	   * - A greek letter                       Unicode: \u0370 - \u03FF
	   * - A mathematical alphanumeric symbol   Unicode: \u{1D400} - \u{1D7FF} excluding invalid code points
	   *
	   * The previous and next characters are needed to determine whether
	   * this character is part of a unicode surrogate pair.
	   *
	   * @param {string} c      Current character in the expression
	   * @param {string} cPrev  Previous character
	   * @param {string} cNext  Next character
	   * @return {boolean}
	   */
	  parse.isAlpha = function isAlpha(c, cPrev, cNext) {
	    return parse.isValidLatinOrGreek(c) || parse.isValidMathSymbol(c, cNext) || parse.isValidMathSymbol(cPrev, c);
	  };

	  /**
	   * Test whether a character is a valid latin, greek, or letter-like character
	   * @param {string} c
	   * @return {boolean}
	   */
	  parse.isValidLatinOrGreek = function isValidLatinOrGreek(c) {
	    return /^[a-zA-Z_$\u00C0-\u02AF\u0370-\u03FF\u2100-\u214F]$/.test(c);
	  };

	  /**
	   * Test whether two given 16 bit characters form a surrogate pair of a
	   * unicode math symbol.
	   *
	   * https://unicode-table.com/en/
	   * https://www.wikiwand.com/en/Mathematical_operators_and_symbols_in_Unicode
	   *
	   * Note: In ES6 will be unicode aware:
	   * https://stackoverflow.com/questions/280712/javascript-unicode-regexes
	   * https://mathiasbynens.be/notes/es6-unicode-regex
	   *
	   * @param {string} high
	   * @param {string} low
	   * @return {boolean}
	   */
	  parse.isValidMathSymbol = function isValidMathSymbol(high, low) {
	    return /^[\uD835]$/.test(high) && /^[\uDC00-\uDFFF]$/.test(low) && /^[^\uDC55\uDC9D\uDCA0\uDCA1\uDCA3\uDCA4\uDCA7\uDCA8\uDCAD\uDCBA\uDCBC\uDCC4\uDD06\uDD0B\uDD0C\uDD15\uDD1D\uDD3A\uDD3F\uDD45\uDD47-\uDD49\uDD51\uDEA6\uDEA7\uDFCC\uDFCD]$/.test(low);
	  };

	  /**
	   * Check whether given character c is a white space character: space, tab, or enter
	   * @param {string} c
	   * @param {number} nestingLevel
	   * @return {boolean}
	   */
	  parse.isWhitespace = function isWhitespace(c, nestingLevel) {
	    // TODO: also take '\r' carriage return as newline? Or does that give problems on mac?
	    return c === ' ' || c === '\t' || c === '\u00A0' || c === '\n' && nestingLevel > 0;
	  };

	  /**
	   * Test whether the character c is a decimal mark (dot).
	   * This is the case when it's not the start of a delimiter '.*', './', or '.^'
	   * @param {string} c
	   * @param {string} cNext
	   * @return {boolean}
	   */
	  parse.isDecimalMark = function isDecimalMark(c, cNext) {
	    return c === '.' && cNext !== '/' && cNext !== '*' && cNext !== '^';
	  };

	  /**
	   * checks if the given char c is a digit or dot
	   * @param {string} c   a string with one character
	   * @return {boolean}
	   */
	  parse.isDigitDot = function isDigitDot(c) {
	    return c >= '0' && c <= '9' || c === '.';
	  };

	  /**
	   * checks if the given char c is a digit
	   * @param {string} c   a string with one character
	   * @return {boolean}
	   */
	  parse.isDigit = function isDigit(c) {
	    return c >= '0' && c <= '9';
	  };

	  /**
	   * Start of the parse levels below, in order of precedence
	   * @return {Node} node
	   * @private
	   */
	  function parseStart(expression, extraNodes) {
	    const state = initialState();
	    (0, _extends2.default)(state, {
	      expression,
	      extraNodes
	    });
	    getToken(state);
	    const node = parseBlock(state);

	    // check for garbage at the end of the expression
	    // an expression ends with a empty character '' and tokenType DELIMITER
	    if (state.token !== '') {
	      if (state.tokenType === TOKENTYPE.DELIMITER) {
	        // user entered a not existing operator like "//"

	        // TODO: give hints for aliases, for example with "<>" give as hint " did you mean !== ?"
	        throw createError(state, 'Unexpected operator ' + state.token);
	      } else {
	        throw createSyntaxError(state, 'Unexpected part "' + state.token + '"');
	      }
	    }
	    return node;
	  }

	  /**
	   * Parse a block with expressions. Expressions can be separated by a newline
	   * character '\n', or by a semicolon ';'. In case of a semicolon, no output
	   * of the preceding line is returned.
	   * @return {Node} node
	   * @private
	   */
	  function parseBlock(state) {
	    let node;
	    const blocks = [];
	    let visible;
	    if (state.token !== '' && state.token !== '\n' && state.token !== ';') {
	      node = parseAssignment(state);
	      if (state.comment) {
	        node.comment = state.comment;
	      }
	    }

	    // TODO: simplify this loop
	    while (state.token === '\n' || state.token === ';') {
	      // eslint-disable-line no-unmodified-loop-condition
	      if (blocks.length === 0 && node) {
	        visible = state.token !== ';';
	        blocks.push({
	          node,
	          visible
	        });
	      }
	      getToken(state);
	      if (state.token !== '\n' && state.token !== ';' && state.token !== '') {
	        node = parseAssignment(state);
	        if (state.comment) {
	          node.comment = state.comment;
	        }
	        visible = state.token !== ';';
	        blocks.push({
	          node,
	          visible
	        });
	      }
	    }
	    if (blocks.length > 0) {
	      return new BlockNode(blocks);
	    } else {
	      if (!node) {
	        node = new ConstantNode(undefined);
	        if (state.comment) {
	          node.comment = state.comment;
	        }
	      }
	      return node;
	    }
	  }

	  /**
	   * Assignment of a function or variable,
	   * - can be a variable like 'a=2.3'
	   * - or a updating an existing variable like 'matrix(2,3:5)=[6,7,8]'
	   * - defining a function like 'f(x) = x^2'
	   * @return {Node} node
	   * @private
	   */
	  function parseAssignment(state) {
	    let name, args, value, valid;
	    const node = parseConditional(state);
	    if (state.token === '=') {
	      if ((0, _is.isSymbolNode)(node)) {
	        // parse a variable assignment like 'a = 2/3'
	        name = node.name;
	        getTokenSkipNewline(state);
	        value = parseAssignment(state);
	        return new AssignmentNode(new SymbolNode(name), value);
	      } else if ((0, _is.isAccessorNode)(node)) {
	        // parse a matrix subset assignment like 'A[1,2] = 4'
	        if (node.optionalChaining) {
	          throw createSyntaxError(state, 'Cannot assign to optional chain');
	        }
	        getTokenSkipNewline(state);
	        value = parseAssignment(state);
	        return new AssignmentNode(node.object, node.index, value);
	      } else if ((0, _is.isFunctionNode)(node) && (0, _is.isSymbolNode)(node.fn)) {
	        // parse function assignment like 'f(x) = x^2'
	        valid = true;
	        args = [];
	        name = node.name;
	        node.args.forEach(function (arg, index) {
	          if ((0, _is.isSymbolNode)(arg)) {
	            args[index] = arg.name;
	          } else {
	            valid = false;
	          }
	        });
	        if (valid) {
	          getTokenSkipNewline(state);
	          value = parseAssignment(state);
	          return new FunctionAssignmentNode(name, args, value);
	        }
	      }
	      throw createSyntaxError(state, 'Invalid left hand side of assignment operator =');
	    }
	    return node;
	  }

	  /**
	   * conditional operation
	   *
	   *     condition ? truePart : falsePart
	   *
	   * Note: conditional operator is right-associative
	   *
	   * @return {Node} node
	   * @private
	   */
	  function parseConditional(state) {
	    let node = parseLogicalOr(state);
	    while (state.token === '?') {
	      // eslint-disable-line no-unmodified-loop-condition
	      // set a conditional level, the range operator will be ignored as long
	      // as conditionalLevel === state.nestingLevel.
	      const prev = state.conditionalLevel;
	      state.conditionalLevel = state.nestingLevel;
	      getTokenSkipNewline(state);
	      const condition = node;
	      const trueExpr = parseAssignment(state);
	      if (state.token !== ':') throw createSyntaxError(state, 'False part of conditional expression expected');
	      state.conditionalLevel = null;
	      getTokenSkipNewline(state);
	      const falseExpr = parseAssignment(state); // Note: check for conditional operator again, right associativity

	      node = new ConditionalNode(condition, trueExpr, falseExpr);

	      // restore the previous conditional level
	      state.conditionalLevel = prev;
	    }
	    return node;
	  }

	  /**
	   * logical or, 'x or y'
	   * @return {Node} node
	   * @private
	   */
	  function parseLogicalOr(state) {
	    let node = parseLogicalXor(state);
	    while (state.token === 'or') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('or', 'or', [node, parseLogicalXor(state)]);
	    }
	    return node;
	  }

	  /**
	   * logical exclusive or, 'x xor y'
	   * @return {Node} node
	   * @private
	   */
	  function parseLogicalXor(state) {
	    let node = parseLogicalAnd(state);
	    while (state.token === 'xor') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('xor', 'xor', [node, parseLogicalAnd(state)]);
	    }
	    return node;
	  }

	  /**
	   * logical and, 'x and y'
	   * @return {Node} node
	   * @private
	   */
	  function parseLogicalAnd(state) {
	    let node = parseBitwiseOr(state);
	    while (state.token === 'and') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('and', 'and', [node, parseBitwiseOr(state)]);
	    }
	    return node;
	  }

	  /**
	   * bitwise or, 'x | y'
	   * @return {Node} node
	   * @private
	   */
	  function parseBitwiseOr(state) {
	    let node = parseBitwiseXor(state);
	    while (state.token === '|') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('|', 'bitOr', [node, parseBitwiseXor(state)]);
	    }
	    return node;
	  }

	  /**
	   * bitwise exclusive or (xor), 'x ^| y'
	   * @return {Node} node
	   * @private
	   */
	  function parseBitwiseXor(state) {
	    let node = parseBitwiseAnd(state);
	    while (state.token === '^|') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('^|', 'bitXor', [node, parseBitwiseAnd(state)]);
	    }
	    return node;
	  }

	  /**
	   * bitwise and, 'x & y'
	   * @return {Node} node
	   * @private
	   */
	  function parseBitwiseAnd(state) {
	    let node = parseRelational(state);
	    while (state.token === '&') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('&', 'bitAnd', [node, parseRelational(state)]);
	    }
	    return node;
	  }

	  /**
	   * Parse a chained conditional, like 'a > b >= c'
	   * @return {Node} node
	   */
	  function parseRelational(state) {
	    const params = [parseShift(state)];
	    const conditionals = [];
	    const operators = {
	      '==': 'equal',
	      '!=': 'unequal',
	      '<': 'smaller',
	      '>': 'larger',
	      '<=': 'smallerEq',
	      '>=': 'largerEq'
	    };
	    while ((0, _object.hasOwnProperty)(operators, state.token)) {
	      // eslint-disable-line no-unmodified-loop-condition
	      const cond = {
	        name: state.token,
	        fn: operators[state.token]
	      };
	      conditionals.push(cond);
	      getTokenSkipNewline(state);
	      params.push(parseShift(state));
	    }
	    if (params.length === 1) {
	      return params[0];
	    } else if (params.length === 2) {
	      return new OperatorNode(conditionals[0].name, conditionals[0].fn, params);
	    } else {
	      return new RelationalNode(conditionals.map(c => c.fn), params);
	    }
	  }

	  /**
	   * Bitwise left shift, bitwise right arithmetic shift, bitwise right logical shift
	   * @return {Node} node
	   * @private
	   */
	  function parseShift(state) {
	    let node, name, fn, params;
	    node = parseConversion(state);
	    const operators = {
	      '<<': 'leftShift',
	      '>>': 'rightArithShift',
	      '>>>': 'rightLogShift'
	    };
	    while ((0, _object.hasOwnProperty)(operators, state.token)) {
	      name = state.token;
	      fn = operators[name];
	      getTokenSkipNewline(state);
	      params = [node, parseConversion(state)];
	      node = new OperatorNode(name, fn, params);
	    }
	    return node;
	  }

	  /**
	   * conversion operators 'to' and 'in'
	   * @return {Node} node
	   * @private
	   */
	  function parseConversion(state) {
	    let node, name, fn, params;
	    node = parseRange(state);
	    const operators = {
	      to: 'to',
	      in: 'to' // alias of 'to'
	    };
	    while ((0, _object.hasOwnProperty)(operators, state.token)) {
	      name = state.token;
	      fn = operators[name];
	      getTokenSkipNewline(state);
	      if (name === 'in' && '])},;'.includes(state.token)) {
	        // end of expression -> this is the unit 'in' ('inch')
	        node = new OperatorNode('*', 'multiply', [node, new SymbolNode('in')], true);
	      } else {
	        // operator 'a to b' or 'a in b'
	        params = [node, parseRange(state)];
	        node = new OperatorNode(name, fn, params);
	      }
	    }
	    return node;
	  }

	  /**
	   * parse range, "start:end", "start:step:end", ":", "start:", ":end", etc
	   * @return {Node} node
	   * @private
	   */
	  function parseRange(state) {
	    let node;
	    const params = [];
	    if (state.token === ':') {
	      if (state.conditionalLevel === state.nestingLevel) {
	        // we are in the midst of parsing a conditional operator, so not
	        // a range, but rather an empty true-expr, which is considered a
	        // syntax error
	        throw createSyntaxError(state, 'The true-expression of a conditional operator may not be empty');
	      } else {
	        // implicit start of range = 1 (one-based)
	        node = new ConstantNode(1);
	      }
	    } else {
	      // explicit start
	      node = parseAddSubtract(state);
	    }
	    if (state.token === ':' && state.conditionalLevel !== state.nestingLevel) {
	      // we ignore the range operator when a conditional operator is being processed on the same level
	      params.push(node);

	      // parse step and end
	      while (state.token === ':' && params.length < 3) {
	        // eslint-disable-line no-unmodified-loop-condition
	        getTokenSkipNewline(state);
	        if (state.token === ')' || state.token === ']' || state.token === ',' || state.token === '') {
	          // implicit end
	          params.push(new SymbolNode('end'));
	        } else {
	          // explicit end
	          params.push(parseAddSubtract(state));
	        }
	      }
	      if (params.length === 3) {
	        // params = [start, step, end]
	        node = new RangeNode(params[0], params[2], params[1]); // start, end, step
	      } else {
	        // length === 2
	        // params = [start, end]
	        node = new RangeNode(params[0], params[1]); // start, end
	      }
	    }
	    return node;
	  }

	  /**
	   * add or subtract
	   * @return {Node} node
	   * @private
	   */
	  function parseAddSubtract(state) {
	    let node, name, fn, params;
	    node = parseMultiplyDivideModulus(state);
	    const operators = {
	      '+': 'add',
	      '-': 'subtract'
	    };
	    while ((0, _object.hasOwnProperty)(operators, state.token)) {
	      name = state.token;
	      fn = operators[name];
	      getTokenSkipNewline(state);
	      const rightNode = parseMultiplyDivideModulus(state);
	      if (rightNode.isPercentage) {
	        params = [node, new OperatorNode('*', 'multiply', [node, rightNode])];
	      } else {
	        params = [node, rightNode];
	      }
	      node = new OperatorNode(name, fn, params);
	    }
	    return node;
	  }

	  /**
	   * multiply, divide, modulus
	   * @return {Node} node
	   * @private
	   */
	  function parseMultiplyDivideModulus(state) {
	    let node, last, name, fn;
	    node = parseImplicitMultiplication(state);
	    last = node;
	    const operators = {
	      '*': 'multiply',
	      '.*': 'dotMultiply',
	      '/': 'divide',
	      './': 'dotDivide',
	      '%': 'mod',
	      mod: 'mod'
	    };
	    while (true) {
	      if ((0, _object.hasOwnProperty)(operators, state.token)) {
	        // explicit operators
	        name = state.token;
	        fn = operators[name];
	        getTokenSkipNewline(state);
	        last = parseImplicitMultiplication(state);
	        node = new OperatorNode(name, fn, [node, last]);
	      } else {
	        break;
	      }
	    }
	    return node;
	  }

	  /**
	   * implicit multiplication
	   * @return {Node} node
	   * @private
	   */
	  function parseImplicitMultiplication(state) {
	    let node, last;
	    node = parseRule2(state);
	    last = node;
	    while (true) {
	      if (state.tokenType === TOKENTYPE.SYMBOL || state.token === 'in' && (0, _is.isConstantNode)(node) || state.token === 'in' && (0, _is.isOperatorNode)(node) && node.fn === 'unaryMinus' && (0, _is.isConstantNode)(node.args[0]) || state.tokenType === TOKENTYPE.NUMBER && !(0, _is.isConstantNode)(last) && (!(0, _is.isOperatorNode)(last) || last.op === '!') || state.token === '(') {
	        // parse implicit multiplication
	        //
	        // symbol:      implicit multiplication like '2a', '(2+3)a', 'a b'
	        // number:      implicit multiplication like '(2+3)2'
	        // parenthesis: implicit multiplication like '2(3+4)', '(3+4)(1+2)'
	        last = parseRule2(state);
	        node = new OperatorNode('*', 'multiply', [node, last], true /* implicit */);
	      } else {
	        break;
	      }
	    }
	    return node;
	  }

	  /**
	   * Infamous "rule 2" as described in https://github.com/josdejong/mathjs/issues/792#issuecomment-361065370
	   * And as amended in https://github.com/josdejong/mathjs/issues/2370#issuecomment-1054052164
	   * Explicit division gets higher precedence than implicit multiplication
	   * when the division matches this pattern:
	   *   [unaryPrefixOp]?[number] / [number] [symbol]
	   * @return {Node} node
	   * @private
	   */
	  function parseRule2(state) {
	    let node = parseUnaryPercentage(state);
	    let last = node;
	    const tokenStates = [];
	    while (true) {
	      // Match the "number /" part of the pattern "number / number symbol"
	      if (state.token === '/' && (0, _is.rule2Node)(last)) {
	        // Look ahead to see if the next token is a number
	        tokenStates.push((0, _extends2.default)({}, state));
	        getTokenSkipNewline(state);

	        // Match the "number / number" part of the pattern
	        if (state.tokenType === TOKENTYPE.NUMBER) {
	          // Look ahead again
	          tokenStates.push((0, _extends2.default)({}, state));
	          getTokenSkipNewline(state);

	          // Match the "symbol" part of the pattern, or a left parenthesis
	          if (state.tokenType === TOKENTYPE.SYMBOL || state.token === '(' || state.token === 'in') {
	            // We've matched the pattern "number / number symbol".
	            // Rewind once and build the "number / number" node; the symbol will be consumed later
	            (0, _extends2.default)(state, tokenStates.pop());
	            tokenStates.pop();
	            last = parseUnaryPercentage(state);
	            node = new OperatorNode('/', 'divide', [node, last]);
	          } else {
	            // Not a match, so rewind
	            tokenStates.pop();
	            (0, _extends2.default)(state, tokenStates.pop());
	            break;
	          }
	        } else {
	          // Not a match, so rewind
	          (0, _extends2.default)(state, tokenStates.pop());
	          break;
	        }
	      } else {
	        break;
	      }
	    }
	    return node;
	  }

	  /**
	   * Unary percentage operator (treated as `value / 100`)
	   * @return {Node} node
	   * @private
	   */
	  function parseUnaryPercentage(state) {
	    let node = parseUnary(state);
	    if (state.token === '%') {
	      const previousState = (0, _extends2.default)({}, state);
	      getTokenSkipNewline(state);
	      // We need to decide if this is a unary percentage % or binary modulo %
	      // So we attempt to parse a unary expression at this point.
	      // If it fails, then the only possibility is that this is a unary percentage.
	      // If it succeeds, then we presume that this must be binary modulo, since the
	      // only things that parseUnary can handle are _higher_ precedence than unary %.
	      try {
	        parseUnary(state);
	        // Not sure if we could somehow use the result of that parseUnary? Without
	        // further analysis/testing, safer just to discard and let the parse proceed
	        (0, _extends2.default)(state, previousState);
	      } catch (_unused) {
	        // Not seeing a term at this point, so was a unary %
	        node = new OperatorNode('/', 'divide', [node, new ConstantNode(100)], false, true);
	      }
	    }
	    return node;
	  }

	  /**
	   * Unary plus and minus, and logical and bitwise not
	   * @return {Node} node
	   * @private
	   */
	  function parseUnary(state) {
	    let name, params, fn;
	    const operators = {
	      '-': 'unaryMinus',
	      '+': 'unaryPlus',
	      '~': 'bitNot',
	      not: 'not'
	    };
	    if ((0, _object.hasOwnProperty)(operators, state.token)) {
	      fn = operators[state.token];
	      name = state.token;
	      getTokenSkipNewline(state);
	      params = [parseUnary(state)];
	      return new OperatorNode(name, fn, params);
	    }
	    return parsePow(state);
	  }

	  /**
	   * power
	   * Note: power operator is right associative
	   * @return {Node} node
	   * @private
	   */
	  function parsePow(state) {
	    let node, name, fn, params;
	    node = parseNullishCoalescing(state);
	    if (state.token === '^' || state.token === '.^') {
	      name = state.token;
	      fn = name === '^' ? 'pow' : 'dotPow';
	      getTokenSkipNewline(state);
	      params = [node, parseUnary(state)]; // Go back to unary, we can have '2^-3'
	      node = new OperatorNode(name, fn, params);
	    }
	    return node;
	  }

	  /**
	   * nullish coalescing operator
	   * @return {Node} node
	   * @private
	   */
	  function parseNullishCoalescing(state) {
	    let node = parseLeftHandOperators(state);
	    while (state.token === '??') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getTokenSkipNewline(state);
	      node = new OperatorNode('??', 'nullish', [node, parseLeftHandOperators(state)]);
	    }
	    return node;
	  }

	  /**
	   * Left hand operators: factorial x!, ctranspose x'
	   * @return {Node} node
	   * @private
	   */
	  function parseLeftHandOperators(state) {
	    let node, name, fn, params;
	    node = parseCustomNodes(state);
	    const operators = {
	      '!': 'factorial',
	      '\'': 'ctranspose'
	    };
	    while ((0, _object.hasOwnProperty)(operators, state.token)) {
	      name = state.token;
	      fn = operators[name];
	      getToken(state);
	      params = [node];
	      node = new OperatorNode(name, fn, params);
	      node = parseAccessors(state, node);
	    }
	    return node;
	  }

	  /**
	   * Parse a custom node handler. A node handler can be used to process
	   * nodes in a custom way, for example for handling a plot.
	   *
	   * A handler must be passed as second argument of the parse function.
	   * - must extend math.Node
	   * - must contain a function _compile(defs: Object) : string
	   * - must contain a function find(filter: Object) : Node[]
	   * - must contain a function toString() : string
	   * - the constructor is called with a single argument containing all parameters
	   *
	   * For example:
	   *
	   *     nodes = {
	   *       'plot': PlotHandler
	   *     }
	   *
	   * The constructor of the handler is called as:
	   *
	   *     node = new PlotHandler(params)
	   *
	   * The handler will be invoked when evaluating an expression like:
	   *
	   *     node = math.parse('plot(sin(x), x)', nodes)
	   *
	   * @return {Node} node
	   * @private
	   */
	  function parseCustomNodes(state) {
	    let params = [];
	    if (state.tokenType === TOKENTYPE.SYMBOL && (0, _object.hasOwnProperty)(state.extraNodes, state.token)) {
	      const CustomNode = state.extraNodes[state.token];
	      getToken(state);

	      // parse parameters
	      if (state.token === '(') {
	        params = [];
	        openParams(state);
	        getToken(state);
	        if (state.token !== ')') {
	          params.push(parseAssignment(state));

	          // parse a list with parameters
	          while (state.token === ',') {
	            // eslint-disable-line no-unmodified-loop-condition
	            getToken(state);
	            params.push(parseAssignment(state));
	          }
	        }
	        if (state.token !== ')') {
	          throw createSyntaxError(state, 'Parenthesis ) expected');
	        }
	        closeParams(state);
	        getToken(state);
	      }

	      // create a new custom node
	      // noinspection JSValidateTypes
	      return new CustomNode(params);
	    }
	    return parseSymbol(state);
	  }

	  /**
	   * parse symbols: functions, variables, constants, units
	   * @return {Node} node
	   * @private
	   */
	  function parseSymbol(state) {
	    let node, name;
	    if (state.tokenType === TOKENTYPE.SYMBOL || state.tokenType === TOKENTYPE.DELIMITER && state.token in NAMED_DELIMITERS) {
	      name = state.token;
	      getToken(state);
	      if ((0, _object.hasOwnProperty)(CONSTANTS, name)) {
	        // true, false, null, ...
	        node = new ConstantNode(CONSTANTS[name]);
	      } else if (NUMERIC_CONSTANTS.includes(name)) {
	        // NaN, Infinity
	        node = new ConstantNode(numeric(name, 'number'));
	      } else {
	        node = new SymbolNode(name);
	      }

	      // parse function parameters and matrix index
	      node = parseAccessors(state, node);
	      return node;
	    }
	    return parseString(state);
	  }

	  /**
	   * parse accessors:
	   * - function invocation in round brackets (...), for example sqrt(2) or sqrt?.(2) with optional chaining
	   * - index enclosed in square brackets [...], for example A[2,3] or A?.[2,3] with optional chaining
	   * - dot notation for properties, like foo.bar or foo?.bar with optional chaining
	   * @param {Object} state
	   * @param {Node} node    Node on which to apply the parameters. If there
	   *                       are no parameters in the expression, the node
	   *                       itself is returned
	   * @param {string[]} [types]  Filter the types of notations
	   *                            can be ['(', '[', '.']
	   * @return {Node} node
	   * @private
	   */
	  function parseAccessors(state, node, types) {
	    let params;

	    // Iterate and handle chained accessors, including repeated optional chaining
	    while (true) {
	      // eslint-disable-line no-unmodified-loop-condition
	      // Track whether an optional chaining operator precedes the next accessor
	      let optional = false;

	      // Consume an optional chaining operator if present
	      if (state.token === '?.') {
	        optional = true;
	        // consume the '?.' token
	        getToken(state);
	      }
	      const hasNextAccessor = (state.token === '(' || state.token === '[' || state.token === '.') && (!types || types.includes(state.token));
	      if (!(optional || hasNextAccessor)) {
	        break;
	      }
	      params = [];
	      if (state.token === '(') {
	        if (optional || (0, _is.isSymbolNode)(node) || (0, _is.isAccessorNode)(node)) {
	          // function invocation: fn(2, 3) or obj.fn(2, 3) or (anything)?.(2, 3)
	          openParams(state);
	          getToken(state);
	          if (state.token !== ')') {
	            params.push(parseAssignment(state));

	            // parse a list with parameters
	            while (state.token === ',') {
	              // eslint-disable-line no-unmodified-loop-condition
	              getToken(state);
	              params.push(parseAssignment(state));
	            }
	          }
	          if (state.token !== ')') {
	            throw createSyntaxError(state, 'Parenthesis ) expected');
	          }
	          closeParams(state);
	          getToken(state);
	          node = new FunctionNode(node, params, optional);
	        } else {
	          // implicit multiplication like (2+3)(4+5) or sqrt(2)(1+2)
	          // don't parse it here but let it be handled by parseImplicitMultiplication
	          // with correct precedence
	          return node;
	        }
	      } else if (state.token === '[') {
	        // index notation like variable[2, 3]
	        openParams(state);
	        getToken(state);
	        if (state.token !== ']') {
	          params.push(parseAssignment(state));

	          // parse a list with parameters
	          while (state.token === ',') {
	            // eslint-disable-line no-unmodified-loop-condition
	            getToken(state);
	            params.push(parseAssignment(state));
	          }
	        }
	        if (state.token !== ']') {
	          throw createSyntaxError(state, 'Parenthesis ] expected');
	        }
	        closeParams(state);
	        getToken(state);
	        node = new AccessorNode(node, new IndexNode(params), optional);
	      } else {
	        // dot notation like variable.prop
	        // consume the `.` (if it was ?., already consumed):
	        if (!optional) getToken(state);
	        const isPropertyName = state.tokenType === TOKENTYPE.SYMBOL || state.tokenType === TOKENTYPE.DELIMITER && state.token in NAMED_DELIMITERS;
	        if (!isPropertyName) {
	          let message = 'Property name expected after ';
	          message += optional ? 'optional chain' : 'dot';
	          throw createSyntaxError(state, message);
	        }
	        params.push(new ConstantNode(state.token));
	        getToken(state);
	        const dotNotation = true;
	        node = new AccessorNode(node, new IndexNode(params, dotNotation), optional);
	      }
	    }
	    return node;
	  }

	  /**
	   * Parse a single or double quoted string.
	   * @return {Node} node
	   * @private
	   */
	  function parseString(state) {
	    let node, str;
	    if (state.token === '"' || state.token === "'") {
	      str = parseStringToken(state, state.token);

	      // create constant
	      node = new ConstantNode(str);

	      // parse index parameters
	      node = parseAccessors(state, node);
	      return node;
	    }
	    return parseMatrix(state);
	  }

	  /**
	   * Parse a string surrounded by single or double quotes
	   * @param {Object} state
	   * @param {"'" | "\""} quote
	   * @return {string}
	   */
	  function parseStringToken(state, quote) {
	    let str = '';
	    while (currentCharacter(state) !== '' && currentCharacter(state) !== quote) {
	      if (currentCharacter(state) === '\\') {
	        next(state);
	        const char = currentCharacter(state);
	        const escapeChar = ESCAPE_CHARACTERS[char];
	        if (escapeChar !== undefined) {
	          // an escaped control character like \" or \n
	          str += escapeChar;
	          state.index += 1;
	        } else if (char === 'u') {
	          // escaped unicode character
	          const unicode = state.expression.slice(state.index + 1, state.index + 5);
	          if (/^[0-9A-Fa-f]{4}$/.test(unicode)) {
	            // test whether the string holds four hexadecimal values
	            str += String.fromCharCode(parseInt(unicode, 16));
	            state.index += 5;
	          } else {
	            throw createSyntaxError(state, `Invalid unicode character \\u${unicode}`);
	          }
	        } else {
	          throw createSyntaxError(state, `Bad escape character \\${char}`);
	        }
	      } else {
	        // any regular character
	        str += currentCharacter(state);
	        next(state);
	      }
	    }
	    getToken(state);
	    if (state.token !== quote) {
	      throw createSyntaxError(state, `End of string ${quote} expected`);
	    }
	    getToken(state);
	    return str;
	  }

	  /**
	   * parse the matrix
	   * @return {Node} node
	   * @private
	   */
	  function parseMatrix(state) {
	    let array, params, rows, cols;
	    if (state.token === '[') {
	      // matrix [...]
	      openParams(state);
	      getToken(state);
	      if (state.token !== ']') {
	        // this is a non-empty matrix
	        const row = parseRow(state);
	        if (state.token === ';') {
	          // 2 dimensional array
	          rows = 1;
	          params = [row];

	          // the rows of the matrix are separated by dot-comma's
	          while (state.token === ';') {
	            // eslint-disable-line no-unmodified-loop-condition
	            getToken(state);
	            if (state.token !== ']') {
	              params[rows] = parseRow(state);
	              rows++;
	            }
	          }
	          if (state.token !== ']') {
	            throw createSyntaxError(state, 'End of matrix ] expected');
	          }
	          closeParams(state);
	          getToken(state);

	          // check if the number of columns matches in all rows
	          cols = params[0].items.length;
	          for (let r = 1; r < rows; r++) {
	            if (params[r].items.length !== cols) {
	              throw createError(state, 'Column dimensions mismatch ' + '(' + params[r].items.length + ' !== ' + cols + ')');
	            }
	          }
	          array = new ArrayNode(params);
	        } else {
	          // 1 dimensional vector
	          if (state.token !== ']') {
	            throw createSyntaxError(state, 'End of matrix ] expected');
	          }
	          closeParams(state);
	          getToken(state);
	          array = row;
	        }
	      } else {
	        // this is an empty matrix "[ ]"
	        closeParams(state);
	        getToken(state);
	        array = new ArrayNode([]);
	      }
	      return parseAccessors(state, array);
	    }
	    return parseObject(state);
	  }

	  /**
	   * Parse a single comma-separated row from a matrix, like 'a, b, c'
	   * @return {ArrayNode} node
	   */
	  function parseRow(state) {
	    const params = [parseAssignment(state)];
	    let len = 1;
	    while (state.token === ',') {
	      // eslint-disable-line no-unmodified-loop-condition
	      getToken(state);

	      // parse expression
	      if (state.token !== ']' && state.token !== ';') {
	        params[len] = parseAssignment(state);
	        len++;
	      }
	    }
	    return new ArrayNode(params);
	  }

	  /**
	   * parse an object, enclosed in angle brackets{...}, for example {value: 2}
	   * @return {Node} node
	   * @private
	   */
	  function parseObject(state) {
	    if (state.token === '{') {
	      openParams(state);
	      let key;
	      const properties = {};
	      do {
	        getToken(state);
	        if (state.token !== '}') {
	          // parse key
	          if (state.token === '"' || state.token === "'") {
	            key = parseStringToken(state, state.token);
	          } else if (state.tokenType === TOKENTYPE.SYMBOL || state.tokenType === TOKENTYPE.DELIMITER && state.token in NAMED_DELIMITERS) {
	            key = state.token;
	            getToken(state);
	          } else {
	            throw createSyntaxError(state, 'Symbol or string expected as object key');
	          }

	          // parse key/value separator
	          if (state.token !== ':') {
	            throw createSyntaxError(state, 'Colon : expected after object key');
	          }
	          getToken(state);

	          // parse key
	          properties[key] = parseAssignment(state);
	        }
	      } while (state.token === ','); // eslint-disable-line no-unmodified-loop-condition

	      if (state.token !== '}') {
	        throw createSyntaxError(state, 'Comma , or bracket } expected after object value');
	      }
	      closeParams(state);
	      getToken(state);
	      let node = new ObjectNode(properties);

	      // parse index parameters
	      node = parseAccessors(state, node);
	      return node;
	    }
	    return parseNumber(state);
	  }

	  /**
	   * parse a number
	   * @return {Node} node
	   * @private
	   */
	  function parseNumber(state) {
	    let numberStr;
	    if (state.tokenType === TOKENTYPE.NUMBER) {
	      // this is a number
	      numberStr = state.token;
	      getToken(state);
	      const numericType = (0, _number.safeNumberType)(numberStr, config);
	      const value = numeric(numberStr, numericType);
	      return new ConstantNode(value);
	    }
	    return parseParentheses(state);
	  }

	  /**
	   * parentheses
	   * @return {Node} node
	   * @private
	   */
	  function parseParentheses(state) {
	    let node;

	    // check if it is a parenthesized expression
	    if (state.token === '(') {
	      // parentheses (...)
	      openParams(state);
	      getToken(state);
	      node = parseAssignment(state); // start again

	      if (state.token !== ')') {
	        throw createSyntaxError(state, 'Parenthesis ) expected');
	      }
	      closeParams(state);
	      getToken(state);
	      node = new ParenthesisNode(node);
	      node = parseAccessors(state, node);
	      return node;
	    }
	    return parseEnd(state);
	  }

	  /**
	   * Evaluated when the expression is not yet ended but expected to end
	   * @return {Node} res
	   * @private
	   */
	  function parseEnd(state) {
	    if (state.token === '') {
	      // syntax error or unexpected end of expression
	      throw createSyntaxError(state, 'Unexpected end of expression');
	    } else {
	      throw createSyntaxError(state, 'Value expected');
	    }
	  }

	  /**
	   * Shortcut for getting the current row value (one based)
	   * Returns the line of the currently handled expression
	   * @private
	   */
	  /* TODO: implement keeping track on the row number
	  function row () {
	    return null
	  }
	  */

	  /**
	   * Shortcut for getting the current col value (one based)
	   * Returns the column (position) where the last state.token starts
	   * @private
	   */
	  function col(state) {
	    return state.index - state.token.length + 1;
	  }

	  /**
	   * Create an error
	   * @param {Object} state
	   * @param {string} message
	   * @return {SyntaxError} instantiated error
	   * @private
	   */
	  function createSyntaxError(state, message) {
	    const c = col(state);
	    const error = new SyntaxError(message + ' (char ' + c + ')');
	    error.char = c;
	    return error;
	  }

	  /**
	   * Create an error
	   * @param {Object} state
	   * @param {string} message
	   * @return {Error} instantiated error
	   * @private
	   */
	  function createError(state, message) {
	    const c = col(state);
	    const error = new SyntaxError(message + ' (char ' + c + ')');
	    error.char = c;
	    return error;
	  }

	  // Now that we can parse, automatically convert strings to Nodes by parsing
	  typed.addConversion({
	    from: 'string',
	    to: 'Node',
	    convert: parse
	  });
	  return parse;
	});
	return parse$1;
}

var compile$2 = {};

var hasRequiredCompile$2;

function requireCompile$2 () {
	if (hasRequiredCompile$2) return compile$2;
	hasRequiredCompile$2 = 1;

	Object.defineProperty(compile$2, "__esModule", {
	  value: true
	});
	compile$2.createCompile = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'compile';
	const dependencies = ['typed', 'parse'];
	compile$2.createCompile = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    parse
	  } = _ref;
	  /**
	   * Parse and compile an expression.
	   * Returns a an object with a function `evaluate([scope])` to evaluate the
	   * compiled expression.
	   *
	   * Syntax:
	   *
	   *     math.compile(expr)                       // returns one node
	   *     math.compile([expr1, expr2, expr3, ...]) // returns an array with nodes
	   *
	   * Examples:
	   *
	   *     const code1 = math.compile('sqrt(3^2 + 4^2)')
	   *     code1.evaluate() // 5
	   *
	   *     let scope = {a: 3, b: 4}
	   *     const code2 = math.compile('a * b') // 12
	   *     code2.evaluate(scope) // 12
	   *     scope.a = 5
	   *     code2.evaluate(scope) // 20
	   *
	   *     const nodes = math.compile(['a = 3', 'b = 4', 'a * b'])
	   *     nodes[2].evaluate() // 12
	   *
	   * See also:
	   *
	   *    parse, evaluate
	   *
	   * @param {string | string[] | Array | Matrix} expr
	   *            The expression to be compiled
	   * @return {{evaluate: Function} | Array.<{evaluate: Function}>} code
	   *            An object with the compiled expression
	   * @throws {Error}
	   */
	  return typed(name, {
	    string: function (expr) {
	      return parse(expr).compile();
	    },
	    'Array | Matrix': function (expr) {
	      return (0, _collection.deepMap)(expr, function (entry) {
	        return parse(entry).compile();
	      });
	    }
	  });
	});
	return compile$2;
}

var evaluate$1 = {};

var hasRequiredEvaluate$1;

function requireEvaluate$1 () {
	if (hasRequiredEvaluate$1) return evaluate$1;
	hasRequiredEvaluate$1 = 1;

	Object.defineProperty(evaluate$1, "__esModule", {
	  value: true
	});
	evaluate$1.createEvaluate = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _map = requireMap$2();
	const name = 'evaluate';
	const dependencies = ['typed', 'parse'];
	evaluate$1.createEvaluate = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    parse
	  } = _ref;
	  /**
	   * Evaluate an expression.
	   *
	   * The expression parser does not use JavaScript. Its syntax is close
	   * to JavaScript but more suited for mathematical expressions.
	   * See [https://mathjs.org/docs/expressions/syntax.html](https://mathjs.org/docs/expressions/syntax.html) to learn
	   * the syntax and get an overview of the exact differences from JavaScript.
	   *
	   * Note the evaluating arbitrary expressions may involve security risks,
	   * see [https://mathjs.org/docs/expressions/security.html](https://mathjs.org/docs/expressions/security.html) for more information.
	   *
	   * Syntax:
	   *
	   *     math.evaluate(expr)
	   *     math.evaluate(expr, scope)
	   *     math.evaluate([expr1, expr2, expr3, ...])
	   *     math.evaluate([expr1, expr2, expr3, ...], scope)
	   *
	   * Example:
	   *
	   *     math.evaluate('(2+3)/4')                // 1.25
	   *     math.evaluate('sqrt(3^2 + 4^2)')        // 5
	   *     math.evaluate('sqrt(-4)')               // 2i
	   *     math.evaluate(['a=3', 'b=4', 'a*b'])    // [3, 4, 12]
	   *
	   *     let scope = {a:3, b:4}
	   *     math.evaluate('a * b', scope)           // 12
	   *
	   * See also:
	   *
	   *    parse, compile
	   *
	   * @param {string | string[] | Matrix} expr   The expression to be evaluated
	   * @param {Object} [scope]                    Scope to read/write variables
	   * @return {*} The result of the expression
	   * @throws {Error}
	   */
	  return typed(name, {
	    string: function (expr) {
	      const scope = (0, _map.createEmptyMap)();
	      return parse(expr).compile().evaluate(scope);
	    },
	    'string, Map | Object': function (expr, scope) {
	      return parse(expr).compile().evaluate(scope);
	    },
	    'Array | Matrix': function (expr) {
	      const scope = (0, _map.createEmptyMap)();
	      return (0, _collection.deepMap)(expr, function (entry) {
	        return parse(entry).compile().evaluate(scope);
	      });
	    },
	    'Array | Matrix, Map | Object': function (expr, scope) {
	      return (0, _collection.deepMap)(expr, function (entry) {
	        return parse(entry).compile().evaluate(scope);
	      });
	    }
	  });
	});
	return evaluate$1;
}

var Parser = {};

var hasRequiredParser$3;

function requireParser$3 () {
	if (hasRequiredParser$3) return Parser;
	hasRequiredParser$3 = 1;

	Object.defineProperty(Parser, "__esModule", {
	  value: true
	});
	Parser.createParserClass = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _map = requireMap$2();
	const name = 'Parser';
	const dependencies = ['evaluate', 'parse'];
	Parser.createParserClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    evaluate,
	    parse
	  } = _ref;
	  /**
	   * @constructor Parser
	   * Parser contains methods to evaluate or parse expressions, and has a number
	   * of convenience methods to get, set, and remove variables from memory. Parser
	   * keeps a scope containing variables in memory, which is used for all
	   * evaluations.
	   *
	   * Methods:
	   *    const result = parser.evaluate(expr)  // evaluate an expression
	   *    const value = parser.get(name)        // retrieve a variable from the parser
	   *    const values = parser.getAll()        // retrieve all defined variables
	   *    parser.set(name, value)               // set a variable in the parser
	   *    parser.remove(name)                   // clear a variable from the
	   *                                          // parsers scope
	   *    parser.clear()                        // clear the parsers scope
	   *
	   * Example usage:
	   *    const parser = new Parser()
	   *    // Note: there is a convenience method which can be used instead:
	   *    // const parser = new math.parser()
	   *
	   *    // evaluate expressions
	   *    parser.evaluate('sqrt(3^2 + 4^2)')        // 5
	   *    parser.evaluate('sqrt(-4)')               // 2i
	   *    parser.evaluate('2 inch in cm')           // 5.08 cm
	   *    parser.evaluate('cos(45 deg)')            // 0.7071067811865476
	   *
	   *    // define variables and functions
	   *    parser.evaluate('x = 7 / 2')              // 3.5
	   *    parser.evaluate('x + 3')                  // 6.5
	   *    parser.evaluate('f(x, y) = x^y')          // f(x, y)
	   *    parser.evaluate('f(2, 3)')                // 8
	   *
	   *    // get and set variables and functions
	   *    const x = parser.get('x')                 // 3.5
	   *    const f = parser.get('f')                 // function
	   *    const g = f(3, 2)                         // 9
	   *    parser.set('h', 500)
	   *    const i = parser.evaluate('h / 2')        // 250
	   *    parser.set('hello', function (name) {
	   *        return 'hello, ' + name + '!'
	   *    })
	   *    parser.evaluate('hello("user")')          // "hello, user!"
	   *
	   *    // clear defined functions and variables
	   *    parser.clear()
	   *
	   */
	  function Parser() {
	    if (!(this instanceof Parser)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    Object.defineProperty(this, 'scope', {
	      value: (0, _map.createEmptyMap)(),
	      writable: false
	    });
	  }

	  /**
	   * Attach type information
	   */
	  Parser.prototype.type = 'Parser';
	  Parser.prototype.isParser = true;

	  /**
	   * Parse and evaluate the given expression
	   * @param {string | string[]} expr   A string containing an expression,
	   *                                   for example "2+3", or a list with expressions
	   * @return {*} result     The result, or undefined when the expression was empty
	   * @throws {Error}
	   */
	  Parser.prototype.evaluate = function (expr) {
	    // TODO: validate arguments
	    return evaluate(expr, this.scope);
	  };

	  /**
	   * Get a variable (a function or variable) by name from the parsers scope.
	   * Returns undefined when not found
	   * @param {string} name
	   * @return {* | undefined} value
	   */
	  Parser.prototype.get = function (name) {
	    // TODO: validate arguments
	    if (this.scope.has(name)) {
	      return this.scope.get(name);
	    }
	  };

	  /**
	   * Get a map with all defined variables
	   * @return {Object} values
	   */
	  Parser.prototype.getAll = function () {
	    return (0, _map.toObject)(this.scope);
	  };

	  /**
	   * Get a map with all defined variables
	   * @return {Map} values
	   */
	  Parser.prototype.getAllAsMap = function () {
	    return this.scope;
	  };
	  function isValidVariableName(name) {
	    if (name.length === 0) {
	      return false;
	    }
	    for (let i = 0; i < name.length; i++) {
	      const cPrev = name.charAt(i - 1);
	      const c = name.charAt(i);
	      const cNext = name.charAt(i + 1);
	      const valid = parse.isAlpha(c, cPrev, cNext) || i > 0 && parse.isDigit(c);
	      if (!valid) {
	        return false;
	      }
	    }
	    return true;
	  }

	  /**
	   * Set a symbol (a function or variable) by name from the parsers scope.
	   * @param {string} name
	   * @param {* | undefined} value
	   */
	  Parser.prototype.set = function (name, value) {
	    if (!isValidVariableName(name)) {
	      throw new Error(`Invalid variable name: '${name}'. Variable names must follow the specified rules.`);
	    }
	    this.scope.set(name, value);
	    return value;
	  };

	  /**
	   * Remove a variable from the parsers scope
	   * @param {string} name
	   */
	  Parser.prototype.remove = function (name) {
	    this.scope.delete(name);
	  };

	  /**
	   * Clear the scope with variables and functions
	   */
	  Parser.prototype.clear = function () {
	    this.scope.clear();
	  };
	  Parser.prototype.toJSON = function () {
	    const json = {
	      mathjs: 'Parser',
	      variables: {},
	      functions: {}
	    };
	    for (const [name, value] of this.scope) {
	      if ((0, _is.isFunction)(value)) {
	        if (!isExpressionFunction(value)) {
	          throw new Error(`Cannot serialize external function ${name}`);
	        }
	        json.functions[name] = `${value.syntax} = ${value.expr}`;
	      } else {
	        json.variables[name] = value;
	      }
	    }
	    return json;
	  };
	  Parser.fromJSON = function (json) {
	    const parser = new Parser();
	    Object.entries(json.variables).forEach(_ref2 => {
	      let [name, value] = _ref2;
	      return parser.set(name, value);
	    });
	    Object.entries(json.functions).forEach(_ref3 => {
	      let [_name, fn] = _ref3;
	      return parser.evaluate(fn);
	    });
	    return parser;
	  };
	  return Parser;
	}, {
	  isClass: true
	});
	function isExpressionFunction(value) {
	  return typeof value === 'function' && typeof value.syntax === 'string' && typeof value.expr === 'string';
	}
	return Parser;
}

var parser$2 = {};

var hasRequiredParser$2;

function requireParser$2 () {
	if (hasRequiredParser$2) return parser$2;
	hasRequiredParser$2 = 1;

	Object.defineProperty(parser$2, "__esModule", {
	  value: true
	});
	parser$2.createParser = void 0;
	var _factory = requireFactory$b();
	const name = 'parser';
	const dependencies = ['typed', 'Parser'];
	parser$2.createParser = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Parser
	  } = _ref;
	  /**
	   * Create a `math.Parser` object that keeps a context of variables and their values, allowing the evaluation of expressions in that context.
	   *
	   * Syntax:
	   *
	   *    math.parser()
	   *
	   * Examples:
	   *
	   *     const parser = new math.parser()
	   *
	   *     // evaluate expressions
	   *     const a = parser.evaluate('sqrt(3^2 + 4^2)') // 5
	   *     const b = parser.evaluate('sqrt(-4)')        // 2i
	   *     const c = parser.evaluate('2 inch in cm')    // 5.08 cm
	   *     const d = parser.evaluate('cos(45 deg)')     // 0.7071067811865476
	   *
	   *     // define variables and functions
	   *     parser.evaluate('x = 7 / 2')             // 3.5
	   *     parser.evaluate('x + 3')                 // 6.5
	   *     parser.evaluate('f(x, y) = x^y')         // f(x, y)
	   *     parser.evaluate('f(2, 3)')               // 8
	   *
	   *     // get and set variables and functions
	   *     const x = parser.get('x')                // 3.5
	   *     const f = parser.get('f')                // function
	   *     const g = f(3, 2)                        // 9
	   *     parser.set('h', 500)
	   *     const i = parser.evaluate('h / 2')       // 250
	   *     parser.set('hello', function (name) {
	   *       return 'hello, ' + name + '!'
	   *     })
	   *     parser.evaluate('hello("user")')         // "hello, user!"
	   *
	   *     // clear defined functions and variables
	   *     parser.clear()
	   *
	   * See also:
	   *
	   *    evaluate, compile, parse
	   *
	   * @return {Parser} Parser
	   */
	  return typed(name, {
	    '': function () {
	      return new Parser();
	    }
	  });
	});
	return parser$2;
}

var lup$1 = {};

var hasRequiredLup$1;

function requireLup$1 () {
	if (hasRequiredLup$1) return lup$1;
	hasRequiredLup$1 = 1;

	Object.defineProperty(lup$1, "__esModule", {
	  value: true
	});
	lup$1.createLup = void 0;
	var _object = requireObject$3();
	var _factory = requireFactory$b();
	const name = 'lup';
	const dependencies = ['typed', 'matrix', 'abs', 'addScalar', 'divideScalar', 'multiplyScalar', 'subtractScalar', 'larger', 'equalScalar', 'unaryMinus', 'DenseMatrix', 'SparseMatrix', 'Spa'];
	lup$1.createLup = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    abs,
	    addScalar,
	    divideScalar,
	    multiplyScalar,
	    subtractScalar,
	    larger,
	    equalScalar,
	    unaryMinus,
	    DenseMatrix,
	    SparseMatrix,
	    Spa
	  } = _ref;
	  /**
	   * Calculate the Matrix LU decomposition with partial pivoting. Matrix `A` is decomposed in two matrices (`L`, `U`) and a
	   * row permutation vector `p` where `A[p,:] = L * U`
	   *
	   * Syntax:
	   *
	   *    math.lup(A)
	   *
	   * Example:
	   *
	   *    const m = [[2, 1], [1, 4]]
	   *    const r = math.lup(m)
	   *    // r = {
	   *    //   L: [[1, 0], [0.5, 1]],
	   *    //   U: [[2, 1], [0, 3.5]],
	   *    //   P: [0, 1]
	   *    // }
	   *
	   * See also:
	   *
	   *    slu, lsolve, lusolve, usolve
	   *
	   * @param {Matrix | Array} A    A two dimensional matrix or array for which to get the LUP decomposition.
	   *
	   * @return {{L: Array | Matrix, U: Array | Matrix, P: Array.<number>}} The lower triangular matrix, the upper triangular matrix and the permutation matrix.
	   */
	  return typed(name, {
	    DenseMatrix: function (m) {
	      return _denseLUP(m);
	    },
	    SparseMatrix: function (m) {
	      return _sparseLUP(m);
	    },
	    Array: function (a) {
	      // create dense matrix from array
	      const m = matrix(a);
	      // lup, use matrix implementation
	      const r = _denseLUP(m);
	      // result
	      return {
	        L: r.L.valueOf(),
	        U: r.U.valueOf(),
	        p: r.p
	      };
	    }
	  });
	  function _denseLUP(m) {
	    // rows & columns
	    const rows = m._size[0];
	    const columns = m._size[1];
	    // minimum rows and columns
	    let n = Math.min(rows, columns);
	    // matrix array, clone original data
	    const data = (0, _object.clone)(m._data);
	    // l matrix arrays
	    const ldata = [];
	    const lsize = [rows, n];
	    // u matrix arrays
	    const udata = [];
	    const usize = [n, columns];
	    // vars
	    let i, j, k;
	    // permutation vector
	    const p = [];
	    for (i = 0; i < rows; i++) {
	      p[i] = i;
	    }
	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // skip first column in upper triangular matrix
	      if (j > 0) {
	        // loop rows
	        for (i = 0; i < rows; i++) {
	          // min i,j
	          const min = Math.min(i, j);
	          // v[i, j]
	          let s = 0;
	          // loop up to min
	          for (k = 0; k < min; k++) {
	            // s = l[i, k] - data[k, j]
	            s = addScalar(s, multiplyScalar(data[i][k], data[k][j]));
	          }
	          data[i][j] = subtractScalar(data[i][j], s);
	        }
	      }
	      // row with larger value in cvector, row >= j
	      let pi = j;
	      let pabsv = 0;
	      let vjj = 0;
	      // loop rows
	      for (i = j; i < rows; i++) {
	        // data @ i, j
	        const v = data[i][j];
	        // absolute value
	        const absv = abs(v);
	        // value is greater than pivote value
	        if (larger(absv, pabsv)) {
	          // store row
	          pi = i;
	          // update max value
	          pabsv = absv;
	          // value @ [j, j]
	          vjj = v;
	        }
	      }
	      // swap rows (j <-> pi)
	      if (j !== pi) {
	        // swap values j <-> pi in p
	        p[j] = [p[pi], p[pi] = p[j]][0];
	        // swap j <-> pi in data
	        DenseMatrix._swapRows(j, pi, data);
	      }
	      // check column is in lower triangular matrix
	      if (j < rows) {
	        // loop rows (lower triangular matrix)
	        for (i = j + 1; i < rows; i++) {
	          // value @ i, j
	          const vij = data[i][j];
	          if (!equalScalar(vij, 0)) {
	            // update data
	            data[i][j] = divideScalar(data[i][j], vjj);
	          }
	        }
	      }
	    }
	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // loop rows
	      for (i = 0; i < rows; i++) {
	        // initialize row in arrays
	        if (j === 0) {
	          // check row exists in upper triangular matrix
	          if (i < columns) {
	            // U
	            udata[i] = [];
	          }
	          // L
	          ldata[i] = [];
	        }
	        // check we are in the upper triangular matrix
	        if (i < j) {
	          // check row exists in upper triangular matrix
	          if (i < columns) {
	            // U
	            udata[i][j] = data[i][j];
	          }
	          // check column exists in lower triangular matrix
	          if (j < rows) {
	            // L
	            ldata[i][j] = 0;
	          }
	          continue;
	        }
	        // diagonal value
	        if (i === j) {
	          // check row exists in upper triangular matrix
	          if (i < columns) {
	            // U
	            udata[i][j] = data[i][j];
	          }
	          // check column exists in lower triangular matrix
	          if (j < rows) {
	            // L
	            ldata[i][j] = 1;
	          }
	          continue;
	        }
	        // check row exists in upper triangular matrix
	        if (i < columns) {
	          // U
	          udata[i][j] = 0;
	        }
	        // check column exists in lower triangular matrix
	        if (j < rows) {
	          // L
	          ldata[i][j] = data[i][j];
	        }
	      }
	    }
	    // l matrix
	    const l = new DenseMatrix({
	      data: ldata,
	      size: lsize
	    });
	    // u matrix
	    const u = new DenseMatrix({
	      data: udata,
	      size: usize
	    });
	    // p vector
	    const pv = [];
	    for (i = 0, n = p.length; i < n; i++) {
	      pv[p[i]] = i;
	    }
	    // return matrices
	    return {
	      L: l,
	      U: u,
	      p: pv,
	      toString: function () {
	        return 'L: ' + this.L.toString() + '\nU: ' + this.U.toString() + '\nP: ' + this.p;
	      }
	    };
	  }
	  function _sparseLUP(m) {
	    // rows & columns
	    const rows = m._size[0];
	    const columns = m._size[1];
	    // minimum rows and columns
	    const n = Math.min(rows, columns);
	    // matrix arrays (will not be modified, thanks to permutation vector)
	    const values = m._values;
	    const index = m._index;
	    const ptr = m._ptr;
	    // l matrix arrays
	    const lvalues = [];
	    const lindex = [];
	    const lptr = [];
	    const lsize = [rows, n];
	    // u matrix arrays
	    const uvalues = [];
	    const uindex = [];
	    const uptr = [];
	    const usize = [n, columns];
	    // vars
	    let i, j, k;
	    // permutation vectors, (current index -> original index) and (original index -> current index)
	    const pvCo = [];
	    const pvOc = [];
	    for (i = 0; i < rows; i++) {
	      pvCo[i] = i;
	      pvOc[i] = i;
	    }
	    // swap indices in permutation vectors (condition x < y)!
	    const swapIndeces = function (x, y) {
	      // find pv indeces getting data from x and y
	      const kx = pvOc[x];
	      const ky = pvOc[y];
	      // update permutation vector current -> original
	      pvCo[kx] = y;
	      pvCo[ky] = x;
	      // update permutation vector original -> current
	      pvOc[x] = ky;
	      pvOc[y] = kx;
	    };
	    // loop columns
	    for (j = 0; j < columns; j++) {
	      // sparse accumulator
	      const spa = new Spa();
	      // check lower triangular matrix has a value @ column j
	      if (j < rows) {
	        // update ptr
	        lptr.push(lvalues.length);
	        // first value in j column for lower triangular matrix
	        lvalues.push(1);
	        lindex.push(j);
	      }
	      // update ptr
	      uptr.push(uvalues.length);
	      // k0 <= k < k1 where k0 = _ptr[j] && k1 = _ptr[j+1]
	      const k0 = ptr[j];
	      const k1 = ptr[j + 1];
	      // copy column j into sparse accumulator
	      for (k = k0; k < k1; k++) {
	        // row
	        i = index[k];
	        // copy column values into sparse accumulator (use permutation vector)
	        spa.set(pvCo[i], values[k]);
	      }
	      // skip first column in upper triangular matrix
	      if (j > 0) {
	        // loop rows in column j (above diagonal)
	        spa.forEach(0, j - 1, function (k, vkj) {
	          // loop rows in column k (L)
	          SparseMatrix._forEachRow(k, lvalues, lindex, lptr, function (i, vik) {
	            // check row is below k
	            if (i > k) {
	              // update spa value
	              spa.accumulate(i, unaryMinus(multiplyScalar(vik, vkj)));
	            }
	          });
	        });
	      }
	      // row with larger value in spa, row >= j
	      let pi = j;
	      let vjj = spa.get(j);
	      let pabsv = abs(vjj);
	      // loop values in spa (order by row, below diagonal)
	      spa.forEach(j + 1, rows - 1, function (x, v) {
	        // absolute value
	        const absv = abs(v);
	        // value is greater than pivote value
	        if (larger(absv, pabsv)) {
	          // store row
	          pi = x;
	          // update max value
	          pabsv = absv;
	          // value @ [j, j]
	          vjj = v;
	        }
	      });
	      // swap rows (j <-> pi)
	      if (j !== pi) {
	        // swap values j <-> pi in L
	        SparseMatrix._swapRows(j, pi, lsize[1], lvalues, lindex, lptr);
	        // swap values j <-> pi in U
	        SparseMatrix._swapRows(j, pi, usize[1], uvalues, uindex, uptr);
	        // swap values in spa
	        spa.swap(j, pi);
	        // update permutation vector (swap values @ j, pi)
	        swapIndeces(j, pi);
	      }
	      // loop values in spa (order by row)
	      spa.forEach(0, rows - 1, function (x, v) {
	        // check we are above diagonal
	        if (x <= j) {
	          // update upper triangular matrix
	          uvalues.push(v);
	          uindex.push(x);
	        } else {
	          // update value
	          v = divideScalar(v, vjj);
	          // check value is non zero
	          if (!equalScalar(v, 0)) {
	            // update lower triangular matrix
	            lvalues.push(v);
	            lindex.push(x);
	          }
	        }
	      });
	    }
	    // update ptrs
	    uptr.push(uvalues.length);
	    lptr.push(lvalues.length);

	    // return matrices
	    return {
	      L: new SparseMatrix({
	        values: lvalues,
	        index: lindex,
	        ptr: lptr,
	        size: lsize
	      }),
	      U: new SparseMatrix({
	        values: uvalues,
	        index: uindex,
	        ptr: uptr,
	        size: usize
	      }),
	      p: pvCo,
	      toString: function () {
	        return 'L: ' + this.L.toString() + '\nU: ' + this.U.toString() + '\nP: ' + this.p;
	      }
	    };
	  }
	});
	return lup$1;
}

var qr$1 = {};

var hasRequiredQr$1;

function requireQr$1 () {
	if (hasRequiredQr$1) return qr$1;
	hasRequiredQr$1 = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(qr$1, "__esModule", {
	  value: true
	});
	qr$1.createQr = void 0;
	var _extends2 = _interopRequireDefault(require_extends());
	var _factory = requireFactory$b();
	const name = 'qr';
	const dependencies = ['typed', 'matrix', 'zeros', 'identity', 'isZero', 'equal', 'sign', 'sqrt', 'conj', 'unaryMinus', 'addScalar', 'divideScalar', 'multiplyScalar', 'subtractScalar', 'complex'];
	qr$1.createQr = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    zeros,
	    identity,
	    isZero,
	    equal,
	    sign,
	    sqrt,
	    conj,
	    unaryMinus,
	    addScalar,
	    divideScalar,
	    multiplyScalar,
	    subtractScalar,
	    complex
	  } = _ref;
	  /**
	   * Calculate the Matrix QR decomposition. Matrix `A` is decomposed in
	   * two matrices (`Q`, `R`) where `Q` is an
	   * orthogonal matrix and `R` is an upper triangular matrix.
	   *
	   * Syntax:
	   *
	   *    math.qr(A)
	   *
	   * Example:
	   *
	   *    const m = [
	   *      [1, -1,  4],
	   *      [1,  4, -2],
	   *      [1,  4,  2],
	   *      [1,  -1, 0]
	   *    ]
	   *    const result = math.qr(m)
	   *    // r = {
	   *    //   Q: [
	   *    //     [0.5, -0.5,   0.5],
	   *    //     [0.5,  0.5,  -0.5],
	   *    //     [0.5,  0.5,   0.5],
	   *    //     [0.5, -0.5,  -0.5],
	   *    //   ],
	   *    //   R: [
	   *    //     [2, 3,  2],
	   *    //     [0, 5, -2],
	   *    //     [0, 0,  4],
	   *    //     [0, 0,  0]
	   *    //   ]
	   *    // }
	   *
	   * See also:
	   *
	   *    lup, lusolve
	   *
	   * @param {Matrix | Array} A    A two dimensional matrix or array
	   * for which to get the QR decomposition.
	   *
	   * @return {{Q: Array | Matrix, R: Array | Matrix}} Q: the orthogonal
	   * matrix and R: the upper triangular matrix
	   */
	  return (0, _extends2.default)(typed(name, {
	    DenseMatrix: function (m) {
	      return _denseQR(m);
	    },
	    SparseMatrix: function (m) {
	      return _sparseQR();
	    },
	    Array: function (a) {
	      // create dense matrix from array
	      const m = matrix(a);
	      // lup, use matrix implementation
	      const r = _denseQR(m);
	      // result
	      return {
	        Q: r.Q.valueOf(),
	        R: r.R.valueOf()
	      };
	    }
	  }), {
	    _denseQRimpl
	  });
	  function _denseQRimpl(m) {
	    // rows & columns (m x n)
	    const rows = m._size[0]; // m
	    const cols = m._size[1]; // n

	    const Q = identity([rows], 'dense');
	    const Qdata = Q._data;
	    const R = m.clone();
	    const Rdata = R._data;

	    // vars
	    let i, j, k;
	    const w = zeros([rows], '');
	    for (k = 0; k < Math.min(cols, rows); ++k) {
	      /*
	       * **k-th Household matrix**
	       *
	       * The matrix I - 2*v*transpose(v)
	       * x     = first column of A
	       * x1    = first element of x
	       * alpha = x1 / |x1| * |x|
	       * e1    = tranpose([1, 0, 0, ...])
	       * u     = x - alpha * e1
	       * v     = u / |u|
	       *
	       * Household matrix = I - 2 * v * tranpose(v)
	       *
	       *  * Initially Q = I and R = A.
	       *  * Household matrix is a reflection in a plane normal to v which
	       *    will zero out all but the top right element in R.
	       *  * Appplying reflection to both Q and R will not change product.
	       *  * Repeat this process on the (1,1) minor to get R as an upper
	       *    triangular matrix.
	       *  * Reflections leave the magnitude of the columns of Q unchanged
	       *    so Q remains othoganal.
	       *
	       */

	      const pivot = Rdata[k][k];
	      const sgn = unaryMinus(equal(pivot, 0) ? 1 : sign(pivot));
	      const conjSgn = conj(sgn);
	      let alphaSquared = 0;
	      for (i = k; i < rows; i++) {
	        alphaSquared = addScalar(alphaSquared, multiplyScalar(Rdata[i][k], conj(Rdata[i][k])));
	      }
	      const alpha = multiplyScalar(sgn, sqrt(alphaSquared));
	      if (!isZero(alpha)) {
	        // first element in vector u
	        const u1 = subtractScalar(pivot, alpha);

	        // w = v * u1 / |u|    (only elements k to (rows-1) are used)
	        w[k] = 1;
	        for (i = k + 1; i < rows; i++) {
	          w[i] = divideScalar(Rdata[i][k], u1);
	        }

	        // tau = - conj(u1 / alpha)
	        const tau = unaryMinus(conj(divideScalar(u1, alpha)));
	        let s;

	        /*
	         * tau and w have been choosen so that
	         *
	         * 2 * v * tranpose(v) = tau * w * tranpose(w)
	         */

	        /*
	         * -- calculate R = R - tau * w * tranpose(w) * R --
	         * Only do calculation with rows k to (rows-1)
	         * Additionally columns 0 to (k-1) will not be changed by this
	         *   multiplication so do not bother recalculating them
	         */
	        for (j = k; j < cols; j++) {
	          s = 0.0;

	          // calculate jth element of [tranpose(w) * R]
	          for (i = k; i < rows; i++) {
	            s = addScalar(s, multiplyScalar(conj(w[i]), Rdata[i][j]));
	          }

	          // calculate the jth element of [tau * transpose(w) * R]
	          s = multiplyScalar(s, tau);
	          for (i = k; i < rows; i++) {
	            Rdata[i][j] = multiplyScalar(subtractScalar(Rdata[i][j], multiplyScalar(w[i], s)), conjSgn);
	          }
	        }
	        /*
	         * -- calculate Q = Q - tau * Q * w * transpose(w) --
	         * Q is a square matrix (rows x rows)
	         * Only do calculation with columns k to (rows-1)
	         * Additionally rows 0 to (k-1) will not be changed by this
	         *   multiplication so do not bother recalculating them
	         */
	        for (i = 0; i < rows; i++) {
	          s = 0.0;

	          // calculate ith element of [Q * w]
	          for (j = k; j < rows; j++) {
	            s = addScalar(s, multiplyScalar(Qdata[i][j], w[j]));
	          }

	          // calculate the ith element of [tau * Q * w]
	          s = multiplyScalar(s, tau);
	          for (j = k; j < rows; ++j) {
	            Qdata[i][j] = divideScalar(subtractScalar(Qdata[i][j], multiplyScalar(s, conj(w[j]))), conjSgn);
	          }
	        }
	      }
	    }

	    // return matrices
	    return {
	      Q,
	      R,
	      toString: function () {
	        return 'Q: ' + this.Q.toString() + '\nR: ' + this.R.toString();
	      }
	    };
	  }
	  function _denseQR(m) {
	    const ret = _denseQRimpl(m);
	    const Rdata = ret.R._data;
	    if (m._data.length > 0) {
	      const zero = Rdata[0][0].type === 'Complex' ? complex(0) : 0;
	      for (let i = 0; i < Rdata.length; ++i) {
	        for (let j = 0; j < i && j < (Rdata[0] || []).length; ++j) {
	          Rdata[i][j] = zero;
	        }
	      }
	    }
	    return ret;
	  }
	  function _sparseQR(m) {
	    throw new Error('qr not implemented for sparse matrices yet');
	  }
	});
	return qr$1;
}

var slu$1 = {};

var csSqr = {};

var csPermute = {};

var hasRequiredCsPermute;

function requireCsPermute () {
	if (hasRequiredCsPermute) return csPermute;
	hasRequiredCsPermute = 1;

	Object.defineProperty(csPermute, "__esModule", {
	  value: true
	});
	csPermute.csPermute = csPermute$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Permutes a sparse matrix C = P * A * Q
	 *
	 * @param {SparseMatrix}  a         The Matrix A
	 * @param {Array}   pinv            The row permutation vector
	 * @param {Array}   q               The column permutation vector
	 * @param {boolean} values          Create a pattern matrix (false), values and pattern otherwise
	 *
	 * @return {Matrix}                 C = P * A * Q, null on error
	 */
	function csPermute$1(a, pinv, q, values) {
	  // a arrays
	  const avalues = a._values;
	  const aindex = a._index;
	  const aptr = a._ptr;
	  const asize = a._size;
	  const adt = a._datatype;
	  // rows & columns
	  const m = asize[0];
	  const n = asize[1];
	  // c arrays
	  const cvalues = values && a._values ? [] : null;
	  const cindex = []; // (aptr[n])
	  const cptr = []; // (n + 1)
	  // initialize vars
	  let nz = 0;
	  // loop columns
	  for (let k = 0; k < n; k++) {
	    // column k of C is column q[k] of A
	    cptr[k] = nz;
	    // apply column permutation
	    const j = q ? q[k] : k;
	    // loop values in column j of A
	    for (let t0 = aptr[j], t1 = aptr[j + 1], t = t0; t < t1; t++) {
	      // row i of A is row pinv[i] of C
	      const r = pinv ? pinv[aindex[t]] : aindex[t];
	      // index
	      cindex[nz] = r;
	      // check we need to populate values
	      if (cvalues) {
	        cvalues[nz] = avalues[t];
	      }
	      // increment number of nonzero elements
	      nz++;
	    }
	  }
	  // finalize the last column of C
	  cptr[n] = nz;
	  // return C matrix
	  return a.createSparseMatrix({
	    values: cvalues,
	    index: cindex,
	    ptr: cptr,
	    size: [m, n],
	    datatype: adt
	  });
	}
	return csPermute;
}

var csPost = {};

var csTdfs = {};

var hasRequiredCsTdfs;

function requireCsTdfs () {
	if (hasRequiredCsTdfs) return csTdfs;
	hasRequiredCsTdfs = 1;

	Object.defineProperty(csTdfs, "__esModule", {
	  value: true
	});
	csTdfs.csTdfs = csTdfs$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Depth-first search and postorder of a tree rooted at node j
	 *
	 * @param {Number}  j               The tree node
	 * @param {Number}  k
	 * @param {Array}   w               The workspace array
	 * @param {Number}  head            The index offset within the workspace for the head array
	 * @param {Number}  next            The index offset within the workspace for the next array
	 * @param {Array}   post            The post ordering array
	 * @param {Number}  stack           The index offset within the workspace for the stack array
	 */
	function csTdfs$1(j, k, w, head, next, post, stack) {
	  // variables
	  let top = 0;
	  // place j on the stack
	  w[stack] = j;
	  // while (stack is not empty)
	  while (top >= 0) {
	    // p = top of stack
	    const p = w[stack + top];
	    // i = youngest child of p
	    const i = w[head + p];
	    if (i === -1) {
	      // p has no unordered children left
	      top--;
	      // node p is the kth postordered node
	      post[k++] = p;
	    } else {
	      // remove i from children of p
	      w[head + p] = w[next + i];
	      // increment top
	      ++top;
	      // start dfs on child node i
	      w[stack + top] = i;
	    }
	  }
	  return k;
	}
	return csTdfs;
}

var hasRequiredCsPost;

function requireCsPost () {
	if (hasRequiredCsPost) return csPost;
	hasRequiredCsPost = 1;

	Object.defineProperty(csPost, "__esModule", {
	  value: true
	});
	csPost.csPost = csPost$1;
	var _csTdfs = requireCsTdfs();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Post order a tree of forest
	 *
	 * @param {Array}   parent          The tree or forest
	 * @param {Number}  n               Number of columns
	 */
	function csPost$1(parent, n) {
	  // check inputs
	  if (!parent) {
	    return null;
	  }
	  // vars
	  let k = 0;
	  let j;
	  // allocate result
	  const post = []; // (n)
	  // workspace, head: first n entries, next: next n entries, stack: last n entries
	  const w = []; // (3 * n)
	  const head = 0;
	  const next = n;
	  const stack = 2 * n;
	  // initialize workspace
	  for (j = 0; j < n; j++) {
	    // empty linked lists
	    w[head + j] = -1;
	  }
	  // traverse nodes in reverse order
	  for (j = n - 1; j >= 0; j--) {
	    // check j is a root
	    if (parent[j] === -1) {
	      continue;
	    }
	    // add j to list of its parent
	    w[next + j] = w[head + parent[j]];
	    w[head + parent[j]] = j;
	  }
	  // loop nodes
	  for (j = 0; j < n; j++) {
	    // skip j if it is not a root
	    if (parent[j] !== -1) {
	      continue;
	    }
	    // depth-first search
	    k = (0, _csTdfs.csTdfs)(j, k, w, head, next, post, stack);
	  }
	  return post;
	}
	return csPost;
}

var csEtree = {};

var hasRequiredCsEtree;

function requireCsEtree () {
	if (hasRequiredCsEtree) return csEtree;
	hasRequiredCsEtree = 1;

	Object.defineProperty(csEtree, "__esModule", {
	  value: true
	});
	csEtree.csEtree = csEtree$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Computes the elimination tree of Matrix A (using triu(A)) or the
	 * elimination tree of A'A without forming A'A.
	 *
	 * @param {Matrix}  a               The A Matrix
	 * @param {boolean} ata             A value of true the function computes the etree of A'A
	 */
	function csEtree$1(a, ata) {
	  // check inputs
	  if (!a) {
	    return null;
	  }
	  // a arrays
	  const aindex = a._index;
	  const aptr = a._ptr;
	  const asize = a._size;
	  // rows & columns
	  const m = asize[0];
	  const n = asize[1];

	  // allocate result
	  const parent = []; // (n)

	  // allocate workspace
	  const w = []; // (n + (ata ? m : 0))
	  const ancestor = 0; // first n entries in w
	  const prev = n; // last m entries (ata = true)

	  let i, inext;

	  // check we are calculating A'A
	  if (ata) {
	    // initialize workspace
	    for (i = 0; i < m; i++) {
	      w[prev + i] = -1;
	    }
	  }
	  // loop columns
	  for (let k = 0; k < n; k++) {
	    // node k has no parent yet
	    parent[k] = -1;
	    // nor does k have an ancestor
	    w[ancestor + k] = -1;
	    // values in column k
	    for (let p0 = aptr[k], p1 = aptr[k + 1], p = p0; p < p1; p++) {
	      // row
	      const r = aindex[p];
	      // node
	      i = ata ? w[prev + r] : r;
	      // traverse from i to k
	      for (; i !== -1 && i < k; i = inext) {
	        // inext = ancestor of i
	        inext = w[ancestor + i];
	        // path compression
	        w[ancestor + i] = k;
	        // check no anc., parent is k
	        if (inext === -1) {
	          parent[i] = k;
	        }
	      }
	      if (ata) {
	        w[prev + r] = k;
	      }
	    }
	  }
	  return parent;
	}
	return csEtree;
}

var csAmd = {};

var csFkeep = {};

var hasRequiredCsFkeep;

function requireCsFkeep () {
	if (hasRequiredCsFkeep) return csFkeep;
	hasRequiredCsFkeep = 1;

	Object.defineProperty(csFkeep, "__esModule", {
	  value: true
	});
	csFkeep.csFkeep = csFkeep$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Keeps entries in the matrix when the callback function returns true, removes the entry otherwise
	 *
	 * @param {Matrix}   a              The sparse matrix
	 * @param {function} callback       The callback function, function will be invoked with the following args:
	 *                                    - The entry row
	 *                                    - The entry column
	 *                                    - The entry value
	 *                                    - The state parameter
	 * @param {any}      other          The state
	 *
	 * @return                          The number of nonzero elements in the matrix
	 */
	function csFkeep$1(a, callback, other) {
	  // a arrays
	  const avalues = a._values;
	  const aindex = a._index;
	  const aptr = a._ptr;
	  const asize = a._size;
	  // columns
	  const n = asize[1];
	  // nonzero items
	  let nz = 0;
	  // loop columns
	  for (let j = 0; j < n; j++) {
	    // get current location of col j
	    let p = aptr[j];
	    // record new location of col j
	    aptr[j] = nz;
	    for (; p < aptr[j + 1]; p++) {
	      // check we need to keep this item
	      if (callback(aindex[p], j, avalues ? avalues[p] : 1, other)) {
	        // keep A(i,j)
	        aindex[nz] = aindex[p];
	        // check we need to process values (pattern only)
	        if (avalues) {
	          avalues[nz] = avalues[p];
	        }
	        // increment nonzero items
	        nz++;
	      }
	    }
	  }
	  // finalize A
	  aptr[n] = nz;
	  // trim arrays
	  aindex.splice(nz, aindex.length - nz);
	  // check we need to process values (pattern only)
	  if (avalues) {
	    avalues.splice(nz, avalues.length - nz);
	  }
	  // return number of nonzero items
	  return nz;
	}
	return csFkeep;
}

var csFlip = {};

var hasRequiredCsFlip;

function requireCsFlip () {
	if (hasRequiredCsFlip) return csFlip;
	hasRequiredCsFlip = 1;

	Object.defineProperty(csFlip, "__esModule", {
	  value: true
	});
	csFlip.csFlip = csFlip$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * This function "flips" its input about the integer -1.
	 *
	 * @param {Number}  i               The value to flip
	 */
	function csFlip$1(i) {
	  // flip the value
	  return -i - 2;
	}
	return csFlip;
}

var hasRequiredCsAmd;

function requireCsAmd () {
	if (hasRequiredCsAmd) return csAmd;
	hasRequiredCsAmd = 1;

	Object.defineProperty(csAmd, "__esModule", {
	  value: true
	});
	csAmd.createCsAmd = void 0;
	var _factory = requireFactory$b();
	var _csFkeep = requireCsFkeep();
	var _csFlip = requireCsFlip();
	var _csTdfs = requireCsTdfs();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	const name = 'csAmd';
	const dependencies = ['add', 'multiply', 'transpose'];
	csAmd.createCsAmd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    add,
	    multiply,
	    transpose
	  } = _ref;
	  /**
	   * Approximate minimum degree ordering. The minimum degree algorithm is a widely used
	   * heuristic for finding a permutation P so that P*A*P' has fewer nonzeros in its factorization
	   * than A. It is a gready method that selects the sparsest pivot row and column during the course
	   * of a right looking sparse Cholesky factorization.
	   *
	   * @param {Number} order    0: Natural, 1: Cholesky, 2: LU, 3: QR
	   * @param {Matrix} m        Sparse Matrix
	   */
	  return function csAmd(order, a) {
	    // check input parameters
	    if (!a || order <= 0 || order > 3) {
	      return null;
	    }
	    // a matrix arrays
	    const asize = a._size;
	    // rows and columns
	    const m = asize[0];
	    const n = asize[1];
	    // initialize vars
	    let lemax = 0;
	    // dense threshold
	    let dense = Math.max(16, 10 * Math.sqrt(n));
	    dense = Math.min(n - 2, dense);
	    // create target matrix C
	    const cm = _createTargetMatrix(order, a, m, n, dense);
	    // drop diagonal entries
	    (0, _csFkeep.csFkeep)(cm, _diag, null);
	    // C matrix arrays
	    const cindex = cm._index;
	    const cptr = cm._ptr;

	    // number of nonzero elements in C
	    let cnz = cptr[n];

	    // allocate result (n+1)
	    const P = [];

	    // create workspace (8 * (n + 1))
	    const W = [];
	    const len = 0; // first n + 1 entries
	    const nv = n + 1; // next n + 1 entries
	    const next = 2 * (n + 1); // next n + 1 entries
	    const head = 3 * (n + 1); // next n + 1 entries
	    const elen = 4 * (n + 1); // next n + 1 entries
	    const degree = 5 * (n + 1); // next n + 1 entries
	    const w = 6 * (n + 1); // next n + 1 entries
	    const hhead = 7 * (n + 1); // last n + 1 entries

	    // use P as workspace for last
	    const last = P;

	    // initialize quotient graph
	    let mark = _initializeQuotientGraph(n, cptr, W, len, head, last, next, hhead, nv, w, elen, degree);

	    // initialize degree lists
	    let nel = _initializeDegreeLists(n, cptr, W, degree, elen, w, dense, nv, head, last, next);

	    // minimum degree node
	    let mindeg = 0;

	    // vars
	    let i, j, k, k1, k2, e, pj, ln, nvi, pk, eln, p1, p2, pn, h, d;

	    // while (selecting pivots) do
	    while (nel < n) {
	      // select node of minimum approximate degree. amd() is now ready to start eliminating the graph. It first
	      // finds a node k of minimum degree and removes it from its degree list. The variable nel keeps track of thow
	      // many nodes have been eliminated.
	      for (k = -1; mindeg < n && (k = W[head + mindeg]) === -1; mindeg++);
	      if (W[next + k] !== -1) {
	        last[W[next + k]] = -1;
	      }
	      // remove k from degree list
	      W[head + mindeg] = W[next + k];
	      // elenk = |Ek|
	      const elenk = W[elen + k];
	      // # of nodes k represents
	      let nvk = W[nv + k];
	      // W[nv + k] nodes of A eliminated
	      nel += nvk;

	      // Construct a new element. The new element Lk is constructed in place if |Ek| = 0. nv[i] is
	      // negated for all nodes i in Lk to flag them as members of this set. Each node i is removed from the
	      // degree lists. All elements e in Ek are absorved into element k.
	      let dk = 0;
	      // flag k as in Lk
	      W[nv + k] = -nvk;
	      let p = cptr[k];
	      // do in place if W[elen + k] === 0
	      const pk1 = elenk === 0 ? p : cnz;
	      let pk2 = pk1;
	      for (k1 = 1; k1 <= elenk + 1; k1++) {
	        if (k1 > elenk) {
	          // search the nodes in k
	          e = k;
	          // list of nodes starts at cindex[pj]
	          pj = p;
	          // length of list of nodes in k
	          ln = W[len + k] - elenk;
	        } else {
	          // search the nodes in e
	          e = cindex[p++];
	          pj = cptr[e];
	          // length of list of nodes in e
	          ln = W[len + e];
	        }
	        for (k2 = 1; k2 <= ln; k2++) {
	          i = cindex[pj++];
	          // check  node i dead, or seen
	          if ((nvi = W[nv + i]) <= 0) {
	            continue;
	          }
	          // W[degree + Lk] += size of node i
	          dk += nvi;
	          // negate W[nv + i] to denote i in Lk
	          W[nv + i] = -nvi;
	          // place i in Lk
	          cindex[pk2++] = i;
	          if (W[next + i] !== -1) {
	            last[W[next + i]] = last[i];
	          }
	          // check we need to remove i from degree list
	          if (last[i] !== -1) {
	            W[next + last[i]] = W[next + i];
	          } else {
	            W[head + W[degree + i]] = W[next + i];
	          }
	        }
	        if (e !== k) {
	          // absorb e into k
	          cptr[e] = (0, _csFlip.csFlip)(k);
	          // e is now a dead element
	          W[w + e] = 0;
	        }
	      }
	      // cindex[cnz...nzmax] is free
	      if (elenk !== 0) {
	        cnz = pk2;
	      }
	      // external degree of k - |Lk\i|
	      W[degree + k] = dk;
	      // element k is in cindex[pk1..pk2-1]
	      cptr[k] = pk1;
	      W[len + k] = pk2 - pk1;
	      // k is now an element
	      W[elen + k] = -2;

	      // Find set differences. The scan1 function now computes the set differences |Le \ Lk| for all elements e. At the start of the
	      // scan, no entry in the w array is greater than or equal to mark.

	      // clear w if necessary
	      mark = _wclear(mark, lemax, W, w, n);
	      // scan 1: find |Le\Lk|
	      for (pk = pk1; pk < pk2; pk++) {
	        i = cindex[pk];
	        // check if W[elen + i] empty, skip it
	        if ((eln = W[elen + i]) <= 0) {
	          continue;
	        }
	        // W[nv + i] was negated
	        nvi = -W[nv + i];
	        const wnvi = mark - nvi;
	        // scan Ei
	        for (p = cptr[i], p1 = cptr[i] + eln - 1; p <= p1; p++) {
	          e = cindex[p];
	          if (W[w + e] >= mark) {
	            // decrement |Le\Lk|
	            W[w + e] -= nvi;
	          } else if (W[w + e] !== 0) {
	            // ensure e is a live element, 1st time e seen in scan 1
	            W[w + e] = W[degree + e] + wnvi;
	          }
	        }
	      }

	      // degree update
	      // The second pass computes the approximate degree di, prunes the sets Ei and Ai, and computes a hash
	      // function h(i) for all nodes in Lk.

	      // scan2: degree update
	      for (pk = pk1; pk < pk2; pk++) {
	        // consider node i in Lk
	        i = cindex[pk];
	        p1 = cptr[i];
	        p2 = p1 + W[elen + i] - 1;
	        pn = p1;
	        // scan Ei
	        for (h = 0, d = 0, p = p1; p <= p2; p++) {
	          e = cindex[p];
	          // check e is an unabsorbed element
	          if (W[w + e] !== 0) {
	            // dext = |Le\Lk|
	            const dext = W[w + e] - mark;
	            if (dext > 0) {
	              // sum up the set differences
	              d += dext;
	              // keep e in Ei
	              cindex[pn++] = e;
	              // compute the hash of node i
	              h += e;
	            } else {
	              // aggressive absorb. e->k
	              cptr[e] = (0, _csFlip.csFlip)(k);
	              // e is a dead element
	              W[w + e] = 0;
	            }
	          }
	        }
	        // W[elen + i] = |Ei|
	        W[elen + i] = pn - p1 + 1;
	        const p3 = pn;
	        const p4 = p1 + W[len + i];
	        // prune edges in Ai
	        for (p = p2 + 1; p < p4; p++) {
	          j = cindex[p];
	          // check node j dead or in Lk
	          const nvj = W[nv + j];
	          if (nvj <= 0) {
	            continue;
	          }
	          // degree(i) += |j|
	          d += nvj;
	          // place j in node list of i
	          cindex[pn++] = j;
	          // compute hash for node i
	          h += j;
	        }
	        // check for mass elimination
	        if (d === 0) {
	          // absorb i into k
	          cptr[i] = (0, _csFlip.csFlip)(k);
	          nvi = -W[nv + i];
	          // |Lk| -= |i|
	          dk -= nvi;
	          // |k| += W[nv + i]
	          nvk += nvi;
	          nel += nvi;
	          W[nv + i] = 0;
	          // node i is dead
	          W[elen + i] = -1;
	        } else {
	          // update degree(i)
	          W[degree + i] = Math.min(W[degree + i], d);
	          // move first node to end
	          cindex[pn] = cindex[p3];
	          // move 1st el. to end of Ei
	          cindex[p3] = cindex[p1];
	          // add k as 1st element in of Ei
	          cindex[p1] = k;
	          // new len of adj. list of node i
	          W[len + i] = pn - p1 + 1;
	          // finalize hash of i
	          h = (h < 0 ? -h : h) % n;
	          // place i in hash bucket
	          W[next + i] = W[hhead + h];
	          W[hhead + h] = i;
	          // save hash of i in last[i]
	          last[i] = h;
	        }
	      }
	      // finalize |Lk|
	      W[degree + k] = dk;
	      lemax = Math.max(lemax, dk);
	      // clear w
	      mark = _wclear(mark + lemax, lemax, W, w, n);

	      // Supernode detection. Supernode detection relies on the hash function h(i) computed for each node i.
	      // If two nodes have identical adjacency lists, their hash functions wil be identical.
	      for (pk = pk1; pk < pk2; pk++) {
	        i = cindex[pk];
	        // check i is dead, skip it
	        if (W[nv + i] >= 0) {
	          continue;
	        }
	        // scan hash bucket of node i
	        h = last[i];
	        i = W[hhead + h];
	        // hash bucket will be empty
	        W[hhead + h] = -1;
	        for (; i !== -1 && W[next + i] !== -1; i = W[next + i], mark++) {
	          ln = W[len + i];
	          eln = W[elen + i];
	          for (p = cptr[i] + 1; p <= cptr[i] + ln - 1; p++) {
	            W[w + cindex[p]] = mark;
	          }
	          let jlast = i;
	          // compare i with all j
	          for (j = W[next + i]; j !== -1;) {
	            let ok = W[len + j] === ln && W[elen + j] === eln;
	            for (p = cptr[j] + 1; ok && p <= cptr[j] + ln - 1; p++) {
	              // compare i and j
	              if (W[w + cindex[p]] !== mark) {
	                ok = 0;
	              }
	            }
	            // check i and j are identical
	            if (ok) {
	              // absorb j into i
	              cptr[j] = (0, _csFlip.csFlip)(i);
	              W[nv + i] += W[nv + j];
	              W[nv + j] = 0;
	              // node j is dead
	              W[elen + j] = -1;
	              // delete j from hash bucket
	              j = W[next + j];
	              W[next + jlast] = j;
	            } else {
	              // j and i are different
	              jlast = j;
	              j = W[next + j];
	            }
	          }
	        }
	      }

	      // Finalize new element. The elimination of node k is nearly complete. All nodes i in Lk are scanned one last time.
	      // Node i is removed from Lk if it is dead. The flagged status of nv[i] is cleared.
	      for (p = pk1, pk = pk1; pk < pk2; pk++) {
	        i = cindex[pk];
	        // check  i is dead, skip it
	        if ((nvi = -W[nv + i]) <= 0) {
	          continue;
	        }
	        // restore W[nv + i]
	        W[nv + i] = nvi;
	        // compute external degree(i)
	        d = W[degree + i] + dk - nvi;
	        d = Math.min(d, n - nel - nvi);
	        if (W[head + d] !== -1) {
	          last[W[head + d]] = i;
	        }
	        // put i back in degree list
	        W[next + i] = W[head + d];
	        last[i] = -1;
	        W[head + d] = i;
	        // find new minimum degree
	        mindeg = Math.min(mindeg, d);
	        W[degree + i] = d;
	        // place i in Lk
	        cindex[p++] = i;
	      }
	      // # nodes absorbed into k
	      W[nv + k] = nvk;
	      // length of adj list of element k
	      if ((W[len + k] = p - pk1) === 0) {
	        // k is a root of the tree
	        cptr[k] = -1;
	        // k is now a dead element
	        W[w + k] = 0;
	      }
	      if (elenk !== 0) {
	        // free unused space in Lk
	        cnz = p;
	      }
	    }

	    // Postordering. The elimination is complete, but no permutation has been computed. All that is left
	    // of the graph is the assembly tree (ptr) and a set of dead nodes and elements (i is a dead node if
	    // nv[i] is zero and a dead element if nv[i] > 0). It is from this information only that the final permutation
	    // is computed. The tree is restored by unflipping all of ptr.

	    // fix assembly tree
	    for (i = 0; i < n; i++) {
	      cptr[i] = (0, _csFlip.csFlip)(cptr[i]);
	    }
	    for (j = 0; j <= n; j++) {
	      W[head + j] = -1;
	    }
	    // place unordered nodes in lists
	    for (j = n; j >= 0; j--) {
	      // skip if j is an element
	      if (W[nv + j] > 0) {
	        continue;
	      }
	      // place j in list of its parent
	      W[next + j] = W[head + cptr[j]];
	      W[head + cptr[j]] = j;
	    }
	    // place elements in lists
	    for (e = n; e >= 0; e--) {
	      // skip unless e is an element
	      if (W[nv + e] <= 0) {
	        continue;
	      }
	      if (cptr[e] !== -1) {
	        // place e in list of its parent
	        W[next + e] = W[head + cptr[e]];
	        W[head + cptr[e]] = e;
	      }
	    }
	    // postorder the assembly tree
	    for (k = 0, i = 0; i <= n; i++) {
	      if (cptr[i] === -1) {
	        k = (0, _csTdfs.csTdfs)(i, k, W, head, next, P, w);
	      }
	    }
	    // remove last item in array
	    P.splice(P.length - 1, 1);
	    // return P
	    return P;
	  };

	  /**
	   * Creates the matrix that will be used by the approximate minimum degree ordering algorithm. The function accepts the matrix M as input and returns a permutation
	   * vector P. The amd algorithm operates on a symmetrix matrix, so one of three symmetric matrices is formed.
	   *
	   * Order: 0
	   *   A natural ordering P=null matrix is returned.
	   *
	   * Order: 1
	   *   Matrix must be square. This is appropriate for a Cholesky or LU factorization.
	   *   P = M + M'
	   *
	   * Order: 2
	   *   Dense columns from M' are dropped, M recreated from M'. This is appropriatefor LU factorization of unsymmetric matrices.
	   *   P = M' * M
	   *
	   * Order: 3
	   *   This is best used for QR factorization or LU factorization is matrix M has no dense rows. A dense row is a row with more than 10*sqr(columns) entries.
	   *   P = M' * M
	   */
	  function _createTargetMatrix(order, a, m, n, dense) {
	    // compute A'
	    const at = transpose(a);

	    // check order = 1, matrix must be square
	    if (order === 1 && n === m) {
	      // C = A + A'
	      return add(a, at);
	    }

	    // check order = 2, drop dense columns from M'
	    if (order === 2) {
	      // transpose arrays
	      const tindex = at._index;
	      const tptr = at._ptr;
	      // new column index
	      let p2 = 0;
	      // loop A' columns (rows)
	      for (let j = 0; j < m; j++) {
	        // column j of AT starts here
	        let p = tptr[j];
	        // new column j starts here
	        tptr[j] = p2;
	        // skip dense col j
	        if (tptr[j + 1] - p > dense) {
	          continue;
	        }
	        // map rows in column j of A
	        for (const p1 = tptr[j + 1]; p < p1; p++) {
	          tindex[p2++] = tindex[p];
	        }
	      }
	      // finalize AT
	      tptr[m] = p2;
	      // recreate A from new transpose matrix
	      a = transpose(at);
	      // use A' * A
	      return multiply(at, a);
	    }

	    // use A' * A, square or rectangular matrix
	    return multiply(at, a);
	  }

	  /**
	   * Initialize quotient graph. There are four kind of nodes and elements that must be represented:
	   *
	   *  - A live node is a node i (or a supernode) that has not been selected as a pivot nad has not been merged into another supernode.
	   *  - A dead node i is one that has been removed from the graph, having been absorved into r = flip(ptr[i]).
	   *  - A live element e is one that is in the graph, having been formed when node e was selected as the pivot.
	   *  - A dead element e is one that has benn absorved into a subsequent element s = flip(ptr[e]).
	   */
	  function _initializeQuotientGraph(n, cptr, W, len, head, last, next, hhead, nv, w, elen, degree) {
	    // Initialize quotient graph
	    for (let k = 0; k < n; k++) {
	      W[len + k] = cptr[k + 1] - cptr[k];
	    }
	    W[len + n] = 0;
	    // initialize workspace
	    for (let i = 0; i <= n; i++) {
	      // degree list i is empty
	      W[head + i] = -1;
	      last[i] = -1;
	      W[next + i] = -1;
	      // hash list i is empty
	      W[hhead + i] = -1;
	      // node i is just one node
	      W[nv + i] = 1;
	      // node i is alive
	      W[w + i] = 1;
	      // Ek of node i is empty
	      W[elen + i] = 0;
	      // degree of node i
	      W[degree + i] = W[len + i];
	    }
	    // clear w
	    const mark = _wclear(0, 0, W, w, n);
	    // n is a dead element
	    W[elen + n] = -2;
	    // n is a root of assembly tree
	    cptr[n] = -1;
	    // n is a dead element
	    W[w + n] = 0;
	    // return mark
	    return mark;
	  }

	  /**
	   * Initialize degree lists. Each node is placed in its degree lists. Nodes of zero degree are eliminated immediately. Nodes with
	   * degree >= dense are alsol eliminated and merged into a placeholder node n, a dead element. Thes nodes will appera last in the
	   * output permutation p.
	   */
	  function _initializeDegreeLists(n, cptr, W, degree, elen, w, dense, nv, head, last, next) {
	    // result
	    let nel = 0;
	    // loop columns
	    for (let i = 0; i < n; i++) {
	      // degree @ i
	      const d = W[degree + i];
	      // check node i is empty
	      if (d === 0) {
	        // element i is dead
	        W[elen + i] = -2;
	        nel++;
	        // i is a root of assembly tree
	        cptr[i] = -1;
	        W[w + i] = 0;
	      } else if (d > dense) {
	        // absorb i into element n
	        W[nv + i] = 0;
	        // node i is dead
	        W[elen + i] = -1;
	        nel++;
	        cptr[i] = (0, _csFlip.csFlip)(n);
	        W[nv + n]++;
	      } else {
	        const h = W[head + d];
	        if (h !== -1) {
	          last[h] = i;
	        }
	        // put node i in degree list d
	        W[next + i] = W[head + d];
	        W[head + d] = i;
	      }
	    }
	    return nel;
	  }
	  function _wclear(mark, lemax, W, w, n) {
	    if (mark < 2 || mark + lemax < 0) {
	      for (let k = 0; k < n; k++) {
	        if (W[w + k] !== 0) {
	          W[w + k] = 1;
	        }
	      }
	      mark = 2;
	    }
	    // at this point, W [0..n-1] < mark holds
	    return mark;
	  }
	  function _diag(i, j) {
	    return i !== j;
	  }
	});
	return csAmd;
}

var csCounts = {};

var csLeaf = {};

var hasRequiredCsLeaf;

function requireCsLeaf () {
	if (hasRequiredCsLeaf) return csLeaf;
	hasRequiredCsLeaf = 1;

	Object.defineProperty(csLeaf, "__esModule", {
	  value: true
	});
	csLeaf.csLeaf = csLeaf$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * This function determines if j is a leaf of the ith row subtree.
	 * Consider A(i,j), node j in ith row subtree and return lca(jprev,j)
	 *
	 * @param {Number}  i               The ith row subtree
	 * @param {Number}  j               The node to test
	 * @param {Array}   w               The workspace array
	 * @param {Number}  first           The index offset within the workspace for the first array
	 * @param {Number}  maxfirst        The index offset within the workspace for the maxfirst array
	 * @param {Number}  prevleaf        The index offset within the workspace for the prevleaf array
	 * @param {Number}  ancestor        The index offset within the workspace for the ancestor array
	 *
	 * @return {Object}
	 */
	function csLeaf$1(i, j, w, first, maxfirst, prevleaf, ancestor) {
	  let s, sparent;

	  // our result
	  let jleaf = 0;
	  let q;

	  // check j is a leaf
	  if (i <= j || w[first + j] <= w[maxfirst + i]) {
	    return -1;
	  }
	  // update max first[j] seen so far
	  w[maxfirst + i] = w[first + j];
	  // jprev = previous leaf of ith subtree
	  const jprev = w[prevleaf + i];
	  w[prevleaf + i] = j;

	  // check j is first or subsequent leaf
	  if (jprev === -1) {
	    // 1st leaf, q = root of ith subtree
	    jleaf = 1;
	    q = i;
	  } else {
	    // update jleaf
	    jleaf = 2;
	    // q = least common ancester (jprev,j)
	    for (q = jprev; q !== w[ancestor + q]; q = w[ancestor + q]);
	    for (s = jprev; s !== q; s = sparent) {
	      // path compression
	      sparent = w[ancestor + s];
	      w[ancestor + s] = q;
	    }
	  }
	  return {
	    jleaf,
	    q
	  };
	}
	return csLeaf;
}

var hasRequiredCsCounts;

function requireCsCounts () {
	if (hasRequiredCsCounts) return csCounts;
	hasRequiredCsCounts = 1;

	Object.defineProperty(csCounts, "__esModule", {
	  value: true
	});
	csCounts.createCsCounts = void 0;
	var _factory = requireFactory$b();
	var _csLeaf = requireCsLeaf();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	const name = 'csCounts';
	const dependencies = ['transpose'];
	csCounts.createCsCounts = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    transpose
	  } = _ref;
	  /**
	   * Computes the column counts using the upper triangular part of A.
	   * It transposes A internally, none of the input parameters are modified.
	   *
	   * @param {Matrix} a           The sparse matrix A
	   *
	   * @param {Matrix} ata         Count the columns of A'A instead
	   *
	   * @return                     An array of size n of the column counts or null on error
	   */
	  return function (a, parent, post, ata) {
	    // check inputs
	    if (!a || !parent || !post) {
	      return null;
	    }
	    // a matrix arrays
	    const asize = a._size;
	    // rows and columns
	    const m = asize[0];
	    const n = asize[1];
	    // variables
	    let i, j, k, J, p, p0, p1;

	    // workspace size
	    const s = 4 * n + (ata ? n + m + 1 : 0);
	    // allocate workspace
	    const w = []; // (s)
	    const ancestor = 0; // first n entries
	    const maxfirst = n; // next n entries
	    const prevleaf = 2 * n; // next n entries
	    const first = 3 * n; // next n entries
	    const head = 4 * n; // next n + 1 entries (used when ata is true)
	    const next = 5 * n + 1; // last entries in workspace
	    // clear workspace w[0..s-1]
	    for (k = 0; k < s; k++) {
	      w[k] = -1;
	    }

	    // allocate result
	    const colcount = []; // (n)

	    // AT = A'
	    const at = transpose(a);
	    // at arrays
	    const tindex = at._index;
	    const tptr = at._ptr;

	    // find w[first + j]
	    for (k = 0; k < n; k++) {
	      j = post[k];
	      // colcount[j]=1 if j is a leaf
	      colcount[j] = w[first + j] === -1 ? 1 : 0;
	      for (; j !== -1 && w[first + j] === -1; j = parent[j]) {
	        w[first + j] = k;
	      }
	    }

	    // initialize ata if needed
	    if (ata) {
	      // invert post
	      for (k = 0; k < n; k++) {
	        w[post[k]] = k;
	      }
	      // loop rows (columns in AT)
	      for (i = 0; i < m; i++) {
	        // values in column i of AT
	        for (k = n, p0 = tptr[i], p1 = tptr[i + 1], p = p0; p < p1; p++) {
	          k = Math.min(k, w[tindex[p]]);
	        }
	        // place row i in linked list k
	        w[next + i] = w[head + k];
	        w[head + k] = i;
	      }
	    }

	    // each node in its own set
	    for (i = 0; i < n; i++) {
	      w[ancestor + i] = i;
	    }
	    for (k = 0; k < n; k++) {
	      // j is the kth node in postordered etree
	      j = post[k];
	      // check j is not a root
	      if (parent[j] !== -1) {
	        colcount[parent[j]]--;
	      }

	      // J=j for LL'=A case
	      for (J = ata ? w[head + k] : j; J !== -1; J = ata ? w[next + J] : -1) {
	        for (p = tptr[J]; p < tptr[J + 1]; p++) {
	          i = tindex[p];
	          const r = (0, _csLeaf.csLeaf)(i, j, w, first, maxfirst, prevleaf, ancestor);
	          // check A(i,j) is in skeleton
	          if (r.jleaf >= 1) {
	            colcount[j]++;
	          }
	          // check account for overlap in q
	          if (r.jleaf === 2) {
	            colcount[r.q]--;
	          }
	        }
	      }
	      if (parent[j] !== -1) {
	        w[ancestor + j] = parent[j];
	      }
	    }
	    // sum up colcount's of each child
	    for (j = 0; j < n; j++) {
	      if (parent[j] !== -1) {
	        colcount[parent[j]] += colcount[j];
	      }
	    }
	    return colcount;
	  };
	});
	return csCounts;
}

var hasRequiredCsSqr;

function requireCsSqr () {
	if (hasRequiredCsSqr) return csSqr;
	hasRequiredCsSqr = 1;

	Object.defineProperty(csSqr, "__esModule", {
	  value: true
	});
	csSqr.createCsSqr = void 0;
	var _csPermute = requireCsPermute();
	var _csPost = requireCsPost();
	var _csEtree = requireCsEtree();
	var _csAmd = requireCsAmd();
	var _csCounts = requireCsCounts();
	var _factory = requireFactory$b();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	const name = 'csSqr';
	const dependencies = ['add', 'multiply', 'transpose'];
	csSqr.createCsSqr = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    add,
	    multiply,
	    transpose
	  } = _ref;
	  const csAmd = (0, _csAmd.createCsAmd)({
	    add,
	    multiply,
	    transpose
	  });
	  const csCounts = (0, _csCounts.createCsCounts)({
	    transpose
	  });

	  /**
	   * Symbolic ordering and analysis for QR and LU decompositions.
	   *
	   * @param {Number}  order           The ordering strategy (see csAmd for more details)
	   * @param {Matrix}  a               The A matrix
	   * @param {boolean} qr              Symbolic ordering and analysis for QR decomposition (true) or
	   *                                  symbolic ordering and analysis for LU decomposition (false)
	   *
	   * @return {Object}                 The Symbolic ordering and analysis for matrix A
	   */
	  return function csSqr(order, a, qr) {
	    // a arrays
	    const aptr = a._ptr;
	    const asize = a._size;
	    // columns
	    const n = asize[1];
	    // vars
	    let k;
	    // symbolic analysis result
	    const s = {};
	    // fill-reducing ordering
	    s.q = csAmd(order, a);
	    // validate results
	    if (order && !s.q) {
	      return null;
	    }
	    // QR symbolic analysis
	    if (qr) {
	      // apply permutations if needed
	      const c = order ? (0, _csPermute.csPermute)(a, null, s.q, 0) : a;
	      // etree of C'*C, where C=A(:,q)
	      s.parent = (0, _csEtree.csEtree)(c, 1);
	      // post order elimination tree
	      const post = (0, _csPost.csPost)(s.parent, n);
	      // col counts chol(C'*C)
	      s.cp = csCounts(c, s.parent, post, 1);
	      // check we have everything needed to calculate number of nonzero elements
	      if (c && s.parent && s.cp && _vcount(c, s)) {
	        // calculate number of nonzero elements
	        for (s.unz = 0, k = 0; k < n; k++) {
	          s.unz += s.cp[k];
	        }
	      }
	    } else {
	      // for LU factorization only, guess nnz(L) and nnz(U)
	      s.unz = 4 * aptr[n] + n;
	      s.lnz = s.unz;
	    }
	    // return result S
	    return s;
	  };

	  /**
	   * Compute nnz(V) = s.lnz, s.pinv, s.leftmost, s.m2 from A and s.parent
	   */
	  function _vcount(a, s) {
	    // a arrays
	    const aptr = a._ptr;
	    const aindex = a._index;
	    const asize = a._size;
	    // rows & columns
	    const m = asize[0];
	    const n = asize[1];
	    // initialize s arrays
	    s.pinv = []; // (m + n)
	    s.leftmost = []; // (m)
	    // vars
	    const parent = s.parent;
	    const pinv = s.pinv;
	    const leftmost = s.leftmost;
	    // workspace, next: first m entries, head: next n entries, tail: next n entries, nque: next n entries
	    const w = []; // (m + 3 * n)
	    const next = 0;
	    const head = m;
	    const tail = m + n;
	    const nque = m + 2 * n;
	    // vars
	    let i, k, p, p0, p1;
	    // initialize w
	    for (k = 0; k < n; k++) {
	      // queue k is empty
	      w[head + k] = -1;
	      w[tail + k] = -1;
	      w[nque + k] = 0;
	    }
	    // initialize row arrays
	    for (i = 0; i < m; i++) {
	      leftmost[i] = -1;
	    }
	    // loop columns backwards
	    for (k = n - 1; k >= 0; k--) {
	      // values & index for column k
	      for (p0 = aptr[k], p1 = aptr[k + 1], p = p0; p < p1; p++) {
	        // leftmost[i] = min(find(A(i,:)))
	        leftmost[aindex[p]] = k;
	      }
	    }
	    // scan rows in reverse order
	    for (i = m - 1; i >= 0; i--) {
	      // row i is not yet ordered
	      pinv[i] = -1;
	      k = leftmost[i];
	      // check row i is empty
	      if (k === -1) {
	        continue;
	      }
	      // first row in queue k
	      if (w[nque + k]++ === 0) {
	        w[tail + k] = i;
	      }
	      // put i at head of queue k
	      w[next + i] = w[head + k];
	      w[head + k] = i;
	    }
	    s.lnz = 0;
	    s.m2 = m;
	    // find row permutation and nnz(V)
	    for (k = 0; k < n; k++) {
	      // remove row i from queue k
	      i = w[head + k];
	      // count V(k,k) as nonzero
	      s.lnz++;
	      // add a fictitious row
	      if (i < 0) {
	        i = s.m2++;
	      }
	      // associate row i with V(:,k)
	      pinv[i] = k;
	      // skip if V(k+1:m,k) is empty
	      if (--nque[k] <= 0) {
	        continue;
	      }
	      // nque[k] is nnz (V(k+1:m,k))
	      s.lnz += w[nque + k];
	      // move all rows to parent of k
	      const pa = parent[k];
	      if (pa !== -1) {
	        if (w[nque + pa] === 0) {
	          w[tail + pa] = w[tail + k];
	        }
	        w[next + w[tail + k]] = w[head + pa];
	        w[head + pa] = w[next + i];
	        w[nque + pa] += w[nque + k];
	      }
	    }
	    for (i = 0; i < m; i++) {
	      if (pinv[i] < 0) {
	        pinv[i] = k++;
	      }
	    }
	    return true;
	  }
	});
	return csSqr;
}

var csLu = {};

var csSpsolve = {};

var csReach = {};

var csMarked = {};

var hasRequiredCsMarked;

function requireCsMarked () {
	if (hasRequiredCsMarked) return csMarked;
	hasRequiredCsMarked = 1;

	Object.defineProperty(csMarked, "__esModule", {
	  value: true
	});
	csMarked.csMarked = csMarked$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Checks if the node at w[j] is marked
	 *
	 * @param {Array}   w               The array
	 * @param {Number}  j               The array index
	 */
	function csMarked$1(w, j) {
	  // check node is marked
	  return w[j] < 0;
	}
	return csMarked;
}

var csMark = {};

var hasRequiredCsMark;

function requireCsMark () {
	if (hasRequiredCsMark) return csMark;
	hasRequiredCsMark = 1;

	Object.defineProperty(csMark, "__esModule", {
	  value: true
	});
	csMark.csMark = csMark$1;
	var _csFlip = requireCsFlip();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Marks the node at w[j]
	 *
	 * @param {Array}   w               The array
	 * @param {Number}  j               The array index
	 */
	function csMark$1(w, j) {
	  // mark w[j]
	  w[j] = (0, _csFlip.csFlip)(w[j]);
	}
	return csMark;
}

var csDfs = {};

var csUnflip = {};

var hasRequiredCsUnflip;

function requireCsUnflip () {
	if (hasRequiredCsUnflip) return csUnflip;
	hasRequiredCsUnflip = 1;

	Object.defineProperty(csUnflip, "__esModule", {
	  value: true
	});
	csUnflip.csUnflip = csUnflip$1;
	var _csFlip = requireCsFlip();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Flips the value if it is negative of returns the same value otherwise.
	 *
	 * @param {Number}  i               The value to flip
	 */
	function csUnflip$1(i) {
	  // flip the value if it is negative
	  return i < 0 ? (0, _csFlip.csFlip)(i) : i;
	}
	return csUnflip;
}

var hasRequiredCsDfs;

function requireCsDfs () {
	if (hasRequiredCsDfs) return csDfs;
	hasRequiredCsDfs = 1;

	Object.defineProperty(csDfs, "__esModule", {
	  value: true
	});
	csDfs.csDfs = csDfs$1;
	var _csMarked = requireCsMarked();
	var _csMark = requireCsMark();
	var _csUnflip = requireCsUnflip();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Depth-first search computes the nonzero pattern xi of the directed graph G (Matrix) starting
	 * at nodes in B (see csReach()).
	 *
	 * @param {Number}  j               The starting node for the DFS algorithm
	 * @param {Matrix}  g               The G matrix to search, ptr array modified, then restored
	 * @param {Number}  top             Start index in stack xi[top..n-1]
	 * @param {Number}  k               The kth column in B
	 * @param {Array}   xi              The nonzero pattern xi[top] .. xi[n - 1], an array of size = 2 * n
	 *                                  The first n entries is the nonzero pattern, the last n entries is the stack
	 * @param {Array}   pinv            The inverse row permutation vector, must be null for L * x = b
	 *
	 * @return {Number}                 New value of top
	 */
	function csDfs$1(j, g, top, xi, pinv) {
	  // g arrays
	  const index = g._index;
	  const ptr = g._ptr;
	  const size = g._size;
	  // columns
	  const n = size[1];
	  // vars
	  let i, p, p2;
	  // initialize head
	  let head = 0;
	  // initialize the recursion stack
	  xi[0] = j;
	  // loop
	  while (head >= 0) {
	    // get j from the top of the recursion stack
	    j = xi[head];
	    // apply permutation vector
	    const jnew = pinv ? pinv[j] : j;
	    // check node j is marked
	    if (!(0, _csMarked.csMarked)(ptr, j)) {
	      // mark node j as visited
	      (0, _csMark.csMark)(ptr, j);
	      // update stack (last n entries in xi)
	      xi[n + head] = jnew < 0 ? 0 : (0, _csUnflip.csUnflip)(ptr[jnew]);
	    }
	    // node j done if no unvisited neighbors
	    let done = 1;
	    // examine all neighbors of j, stack (last n entries in xi)
	    for (p = xi[n + head], p2 = jnew < 0 ? 0 : (0, _csUnflip.csUnflip)(ptr[jnew + 1]); p < p2; p++) {
	      // consider neighbor node i
	      i = index[p];
	      // check we have visited node i, skip it
	      if ((0, _csMarked.csMarked)(ptr, i)) {
	        continue;
	      }
	      // pause depth-first search of node j, update stack (last n entries in xi)
	      xi[n + head] = p;
	      // start dfs at node i
	      xi[++head] = i;
	      // node j is not done
	      done = 0;
	      // break, to start dfs(i)
	      break;
	    }
	    // check depth-first search at node j is done
	    if (done) {
	      // remove j from the recursion stack
	      head--;
	      // and place in the output stack
	      xi[--top] = j;
	    }
	  }
	  return top;
	}
	return csDfs;
}

var hasRequiredCsReach;

function requireCsReach () {
	if (hasRequiredCsReach) return csReach;
	hasRequiredCsReach = 1;

	Object.defineProperty(csReach, "__esModule", {
	  value: true
	});
	csReach.csReach = csReach$1;
	var _csMarked = requireCsMarked();
	var _csMark = requireCsMark();
	var _csDfs = requireCsDfs();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * The csReach function computes X = Reach(B), where B is the nonzero pattern of the n-by-1
	 * sparse column of vector b. The function returns the set of nodes reachable from any node in B. The
	 * nonzero pattern xi of the solution x to the sparse linear system Lx=b is given by X=Reach(B).
	 *
	 * @param {Matrix}  g               The G matrix
	 * @param {Matrix}  b               The B matrix
	 * @param {Number}  k               The kth column in B
	 * @param {Array}   xi              The nonzero pattern xi[top] .. xi[n - 1], an array of size = 2 * n
	 *                                  The first n entries is the nonzero pattern, the last n entries is the stack
	 * @param {Array}   pinv            The inverse row permutation vector
	 *
	 * @return {Number}                 The index for the nonzero pattern
	 */
	function csReach$1(g, b, k, xi, pinv) {
	  // g arrays
	  const gptr = g._ptr;
	  const gsize = g._size;
	  // b arrays
	  const bindex = b._index;
	  const bptr = b._ptr;
	  // columns
	  const n = gsize[1];
	  // vars
	  let p, p0, p1;
	  // initialize top
	  let top = n;
	  // loop column indeces in B
	  for (p0 = bptr[k], p1 = bptr[k + 1], p = p0; p < p1; p++) {
	    // node i
	    const i = bindex[p];
	    // check node i is marked
	    if (!(0, _csMarked.csMarked)(gptr, i)) {
	      // start a dfs at unmarked node i
	      top = (0, _csDfs.csDfs)(i, g, top, xi, pinv);
	    }
	  }
	  // loop columns from top -> n - 1
	  for (p = top; p < n; p++) {
	    // restore G
	    (0, _csMark.csMark)(gptr, xi[p]);
	  }
	  return top;
	}
	return csReach;
}

var hasRequiredCsSpsolve;

function requireCsSpsolve () {
	if (hasRequiredCsSpsolve) return csSpsolve;
	hasRequiredCsSpsolve = 1;

	Object.defineProperty(csSpsolve, "__esModule", {
	  value: true
	});
	csSpsolve.createCsSpsolve = void 0;
	var _csReach = requireCsReach();
	var _factory = requireFactory$b();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	const name = 'csSpsolve';
	const dependencies = ['divideScalar', 'multiply', 'subtract'];
	csSpsolve.createCsSpsolve = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    divideScalar,
	    multiply,
	    subtract
	  } = _ref;
	  /**
	   * The function csSpsolve() computes the solution to G * x = bk, where bk is the
	   * kth column of B. When lo is true, the function assumes G = L is lower triangular with the
	   * diagonal entry as the first entry in each column. When lo is true, the function assumes G = U
	   * is upper triangular with the diagonal entry as the last entry in each column.
	   *
	   * @param {Matrix}  g               The G matrix
	   * @param {Matrix}  b               The B matrix
	   * @param {Number}  k               The kth column in B
	   * @param {Array}   xi              The nonzero pattern xi[top] .. xi[n - 1], an array of size = 2 * n
	   *                                  The first n entries is the nonzero pattern, the last n entries is the stack
	   * @param {Array}   x               The soluton to the linear system G * x = b
	   * @param {Array}   pinv            The inverse row permutation vector, must be null for L * x = b
	   * @param {boolean} lo              The lower (true) upper triangular (false) flag
	   *
	   * @return {Number}                 The index for the nonzero pattern
	   */
	  return function csSpsolve(g, b, k, xi, x, pinv, lo) {
	    // g arrays
	    const gvalues = g._values;
	    const gindex = g._index;
	    const gptr = g._ptr;
	    const gsize = g._size;
	    // columns
	    const n = gsize[1];
	    // b arrays
	    const bvalues = b._values;
	    const bindex = b._index;
	    const bptr = b._ptr;
	    // vars
	    let p, p0, p1, q;
	    // xi[top..n-1] = csReach(B(:,k))
	    const top = (0, _csReach.csReach)(g, b, k, xi, pinv);
	    // clear x
	    for (p = top; p < n; p++) {
	      x[xi[p]] = 0;
	    }
	    // scatter b
	    for (p0 = bptr[k], p1 = bptr[k + 1], p = p0; p < p1; p++) {
	      x[bindex[p]] = bvalues[p];
	    }
	    // loop columns
	    for (let px = top; px < n; px++) {
	      // x array index for px
	      const j = xi[px];
	      // apply permutation vector (U x = b), j maps to column J of G
	      const J = pinv ? pinv[j] : j;
	      // check column J is empty
	      if (J < 0) {
	        continue;
	      }
	      // column value indeces in G, p0 <= p < p1
	      p0 = gptr[J];
	      p1 = gptr[J + 1];
	      // x(j) /= G(j,j)
	      x[j] = divideScalar(x[j], gvalues[lo ? p0 : p1 - 1]);
	      // first entry L(j,j)
	      p = lo ? p0 + 1 : p0;
	      q = lo ? p1 : p1 - 1;
	      // loop
	      for (; p < q; p++) {
	        // row
	        const i = gindex[p];
	        // x(i) -= G(i,j) * x(j)
	        x[i] = subtract(x[i], multiply(gvalues[p], x[j]));
	      }
	    }
	    // return top of stack
	    return top;
	  };
	});
	return csSpsolve;
}

var hasRequiredCsLu;

function requireCsLu () {
	if (hasRequiredCsLu) return csLu;
	hasRequiredCsLu = 1;

	Object.defineProperty(csLu, "__esModule", {
	  value: true
	});
	csLu.createCsLu = void 0;
	var _factory = requireFactory$b();
	var _csSpsolve = requireCsSpsolve();
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	const name = 'csLu';
	const dependencies = ['abs', 'divideScalar', 'multiply', 'subtract', 'larger', 'largerEq', 'SparseMatrix'];
	csLu.createCsLu = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    abs,
	    divideScalar,
	    multiply,
	    subtract,
	    larger,
	    largerEq,
	    SparseMatrix
	  } = _ref;
	  const csSpsolve = (0, _csSpsolve.createCsSpsolve)({
	    divideScalar,
	    multiply,
	    subtract
	  });

	  /**
	   * Computes the numeric LU factorization of the sparse matrix A. Implements a Left-looking LU factorization
	   * algorithm that computes L and U one column at a tume. At the kth step, it access columns 1 to k-1 of L
	   * and column k of A. Given the fill-reducing column ordering q (see parameter s) computes L, U and pinv so
	   * L * U = A(p, q), where p is the inverse of pinv.
	   *
	   * @param {Matrix}  m               The A Matrix to factorize
	   * @param {Object}  s               The symbolic analysis from csSqr(). Provides the fill-reducing
	   *                                  column ordering q
	   * @param {Number}  tol             Partial pivoting threshold (1 for partial pivoting)
	   *
	   * @return {Number}                 The numeric LU factorization of A or null
	   */
	  return function csLu(m, s, tol) {
	    // validate input
	    if (!m) {
	      return null;
	    }
	    // m arrays
	    const size = m._size;
	    // columns
	    const n = size[1];
	    // symbolic analysis result
	    let q;
	    let lnz = 100;
	    let unz = 100;
	    // update symbolic analysis parameters
	    if (s) {
	      q = s.q;
	      lnz = s.lnz || lnz;
	      unz = s.unz || unz;
	    }
	    // L arrays
	    const lvalues = []; // (lnz)
	    const lindex = []; // (lnz)
	    const lptr = []; // (n + 1)
	    // L
	    const L = new SparseMatrix({
	      values: lvalues,
	      index: lindex,
	      ptr: lptr,
	      size: [n, n]
	    });
	    // U arrays
	    const uvalues = []; // (unz)
	    const uindex = []; // (unz)
	    const uptr = []; // (n + 1)
	    // U
	    const U = new SparseMatrix({
	      values: uvalues,
	      index: uindex,
	      ptr: uptr,
	      size: [n, n]
	    });
	    // inverse of permutation vector
	    const pinv = []; // (n)
	    // vars
	    let i, p;
	    // allocate arrays
	    const x = []; // (n)
	    const xi = []; // (2 * n)
	    // initialize variables
	    for (i = 0; i < n; i++) {
	      // clear workspace
	      x[i] = 0;
	      // no rows pivotal yet
	      pinv[i] = -1;
	      // no cols of L yet
	      lptr[i + 1] = 0;
	    }
	    // reset number of nonzero elements in L and U
	    lnz = 0;
	    unz = 0;
	    // compute L(:,k) and U(:,k)
	    for (let k = 0; k < n; k++) {
	      // update ptr
	      lptr[k] = lnz;
	      uptr[k] = unz;
	      // apply column permutations if needed
	      const col = q ? q[k] : k;
	      // solve triangular system, x = L\A(:,col)
	      const top = csSpsolve(L, m, col, xi, x, pinv, 1);
	      // find pivot
	      let ipiv = -1;
	      let a = -1;
	      // loop xi[] from top -> n
	      for (p = top; p < n; p++) {
	        // x[i] is nonzero
	        i = xi[p];
	        // check row i is not yet pivotal
	        if (pinv[i] < 0) {
	          // absolute value of x[i]
	          const xabs = abs(x[i]);
	          // check absoulte value is greater than pivot value
	          if (larger(xabs, a)) {
	            // largest pivot candidate so far
	            a = xabs;
	            ipiv = i;
	          }
	        } else {
	          // x(i) is the entry U(pinv[i],k)
	          uindex[unz] = pinv[i];
	          uvalues[unz++] = x[i];
	        }
	      }
	      // validate we found a valid pivot
	      if (ipiv === -1 || a <= 0) {
	        return null;
	      }
	      // update actual pivot column, give preference to diagonal value
	      if (pinv[col] < 0 && largerEq(abs(x[col]), multiply(a, tol))) {
	        ipiv = col;
	      }
	      // the chosen pivot
	      const pivot = x[ipiv];
	      // last entry in U(:,k) is U(k,k)
	      uindex[unz] = k;
	      uvalues[unz++] = pivot;
	      // ipiv is the kth pivot row
	      pinv[ipiv] = k;
	      // first entry in L(:,k) is L(k,k) = 1
	      lindex[lnz] = ipiv;
	      lvalues[lnz++] = 1;
	      // L(k+1:n,k) = x / pivot
	      for (p = top; p < n; p++) {
	        // row
	        i = xi[p];
	        // check x(i) is an entry in L(:,k)
	        if (pinv[i] < 0) {
	          // save unpermuted row in L
	          lindex[lnz] = i;
	          // scale pivot column
	          lvalues[lnz++] = divideScalar(x[i], pivot);
	        }
	        // x[0..n-1] = 0 for next k
	        x[i] = 0;
	      }
	    }
	    // update ptr
	    lptr[n] = lnz;
	    uptr[n] = unz;
	    // fix row indices of L for final pinv
	    for (p = 0; p < lnz; p++) {
	      lindex[p] = pinv[lindex[p]];
	    }
	    // trim arrays
	    lvalues.splice(lnz, lvalues.length - lnz);
	    lindex.splice(lnz, lindex.length - lnz);
	    uvalues.splice(unz, uvalues.length - unz);
	    uindex.splice(unz, uindex.length - unz);
	    // return LU factor
	    return {
	      L,
	      U,
	      pinv
	    };
	  };
	});
	return csLu;
}

var hasRequiredSlu$1;

function requireSlu$1 () {
	if (hasRequiredSlu$1) return slu$1;
	hasRequiredSlu$1 = 1;

	Object.defineProperty(slu$1, "__esModule", {
	  value: true
	});
	slu$1.createSlu = void 0;
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	var _csSqr = requireCsSqr();
	var _csLu = requireCsLu();
	const name = 'slu';
	const dependencies = ['typed', 'abs', 'add', 'multiply', 'transpose', 'divideScalar', 'subtract', 'larger', 'largerEq', 'SparseMatrix'];
	slu$1.createSlu = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    abs,
	    add,
	    multiply,
	    transpose,
	    divideScalar,
	    subtract,
	    larger,
	    largerEq,
	    SparseMatrix
	  } = _ref;
	  const csSqr = (0, _csSqr.createCsSqr)({
	    add,
	    multiply,
	    transpose
	  });
	  const csLu = (0, _csLu.createCsLu)({
	    abs,
	    divideScalar,
	    multiply,
	    subtract,
	    larger,
	    largerEq,
	    SparseMatrix
	  });

	  /**
	   * Calculate the Sparse Matrix LU decomposition with full pivoting. Sparse Matrix `A` is decomposed in two matrices (`L`, `U`) and two permutation vectors (`pinv`, `q`) where
	   *
	   * `P * A * Q = L * U`
	   *
	   * Syntax:
	   *
	   *    math.slu(A, order, threshold)
	   *
	   * Examples:
	   *
	   *    const A = math.sparse([[4,3], [6, 3]])
	   *    math.slu(A, 1, 0.001)
	   *    // returns:
	   *    // {
	   *    //   L: [[1, 0], [1.5, 1]]
	   *    //   U: [[4, 3], [0, -1.5]]
	   *    //   p: [0, 1]
	   *    //   q: [0, 1]
	   *    // }
	   *
	   * See also:
	   *
	   *    lup, lsolve, usolve, lusolve
	   *
	   * @param {SparseMatrix} A              A two dimensional sparse matrix for which to get the LU decomposition.
	   * @param {Number}       order          The Symbolic Ordering and Analysis order:
	   *                                       0 - Natural ordering, no permutation vector q is returned
	   *                                       1 - Matrix must be square, symbolic ordering and analisis is performed on M = A + A'
	   *                                       2 - Symbolic ordering and analisis is performed on M = A' * A. Dense columns from A' are dropped, A recreated from A'.
	   *                                           This is appropriatefor LU factorization of unsymmetric matrices.
	   *                                       3 - Symbolic ordering and analisis is performed on M = A' * A. This is best used for LU factorization is matrix M has no dense rows.
	   *                                           A dense row is a row with more than 10*sqr(columns) entries.
	   * @param {Number}       threshold       Partial pivoting threshold (1 for partial pivoting)
	   *
	   * @return {Object} The lower triangular matrix, the upper triangular matrix and the permutation vectors.
	   */
	  return typed(name, {
	    'SparseMatrix, number, number': function (a, order, threshold) {
	      // verify order
	      if (!(0, _number.isInteger)(order) || order < 0 || order > 3) {
	        throw new Error('Symbolic Ordering and Analysis order must be an integer number in the interval [0, 3]');
	      }
	      // verify threshold
	      if (threshold < 0 || threshold > 1) {
	        throw new Error('Partial pivoting threshold must be a number from 0 to 1');
	      }

	      // perform symbolic ordering and analysis
	      const s = csSqr(order, a, false);

	      // perform lu decomposition
	      const f = csLu(a, s, threshold);

	      // return decomposition
	      return {
	        L: f.L,
	        U: f.U,
	        p: f.pinv,
	        q: s.q,
	        toString: function () {
	          return 'L: ' + this.L.toString() + '\nU: ' + this.U.toString() + '\np: ' + this.p.toString() + (this.q ? '\nq: ' + this.q.toString() : '') + '\n';
	        }
	      };
	    }
	  });
	});
	return slu$1;
}

var lusolve$1 = {};

var csIpvec = {};

var hasRequiredCsIpvec;

function requireCsIpvec () {
	if (hasRequiredCsIpvec) return csIpvec;
	hasRequiredCsIpvec = 1;

	Object.defineProperty(csIpvec, "__esModule", {
	  value: true
	});
	csIpvec.csIpvec = csIpvec$1;
	// Copyright (c) 2006-2024, Timothy A. Davis, All Rights Reserved.
	// SPDX-License-Identifier: LGPL-2.1+
	// https://github.com/DrTimothyAldenDavis/SuiteSparse/tree/dev/CSparse/Source

	/**
	 * Permutes a vector; x = P'b. In MATLAB notation, x(p)=b.
	 *
	 * @param {Array} p           The permutation vector of length n. null value denotes identity
	 * @param {Array} b           The input vector
	 *
	 * @return {Array}            The output vector x = P'b
	 */
	function csIpvec$1(p, b) {
	  // vars
	  let k;
	  const n = b.length;
	  const x = [];
	  // check permutation vector was provided, p = null denotes identity
	  if (p) {
	    // loop vector
	    for (k = 0; k < n; k++) {
	      // apply permutation
	      x[p[k]] = b[k];
	    }
	  } else {
	    // loop vector
	    for (k = 0; k < n; k++) {
	      // x[i] = b[i]
	      x[k] = b[k];
	    }
	  }
	  return x;
	}
	return csIpvec;
}

var hasRequiredLusolve$1;

function requireLusolve$1 () {
	if (hasRequiredLusolve$1) return lusolve$1;
	hasRequiredLusolve$1 = 1;

	Object.defineProperty(lusolve$1, "__esModule", {
	  value: true
	});
	lusolve$1.createLusolve = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _solveValidation = requireSolveValidation();
	var _csIpvec = requireCsIpvec();
	const name = 'lusolve';
	const dependencies = ['typed', 'matrix', 'lup', 'slu', 'usolve', 'lsolve', 'DenseMatrix'];
	lusolve$1.createLusolve = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    lup,
	    slu,
	    usolve,
	    lsolve,
	    DenseMatrix
	  } = _ref;
	  const solveValidation = (0, _solveValidation.createSolveValidation)({
	    DenseMatrix
	  });

	  /**
	   * Solves the linear system `A * x = b` where `A` is an [n x n] matrix and `b` is a [n] column vector.
	   *
	   * Syntax:
	   *
	   *    math.lusolve(A, b)     // returns column vector with the solution to the linear system A * x = b
	   *    math.lusolve(lup, b)   // returns column vector with the solution to the linear system A * x = b, lup = math.lup(A)
	   *
	   * Examples:
	   *
	   *    const m = [[1, 0, 0, 0], [0, 2, 0, 0], [0, 0, 3, 0], [0, 0, 0, 4]]
	   *
	   *    const x = math.lusolve(m, [-1, -1, -1, -1])        // x = [[-1], [-0.5], [-1/3], [-0.25]]
	   *
	   *    const f = math.lup(m)
	   *    const x1 = math.lusolve(f, [-1, -1, -1, -1])       // x1 = [[-1], [-0.5], [-1/3], [-0.25]]
	   *    const x2 = math.lusolve(f, [1, 2, 1, -1])          // x2 = [[1], [1], [1/3], [-0.25]]
	   *
	   *    const a = [[-2, 3], [2, 1]]
	   *    const b = [11, 9]
	   *    const x = math.lusolve(a, b)  // [[2], [5]]
	   *
	   * See also:
	   *
	   *    lup, slu, lsolve, usolve
	   *
	   * @param {Matrix | Array | Object} A      Invertible Matrix or the Matrix LU decomposition
	   * @param {Matrix | Array} b               Column Vector
	   * @param {number} [order]                 The Symbolic Ordering and Analysis order, see slu for details. Matrix must be a SparseMatrix
	   * @param {Number} [threshold]             Partial pivoting threshold (1 for partial pivoting), see slu for details. Matrix must be a SparseMatrix.
	   *
	   * @return {DenseMatrix | Array}           Column vector with the solution to the linear system A * x = b
	   */
	  return typed(name, {
	    'Array, Array | Matrix': function (a, b) {
	      a = matrix(a);
	      const d = lup(a);
	      const x = _lusolve(d.L, d.U, d.p, null, b);
	      return x.valueOf();
	    },
	    'DenseMatrix, Array | Matrix': function (a, b) {
	      const d = lup(a);
	      return _lusolve(d.L, d.U, d.p, null, b);
	    },
	    'SparseMatrix, Array | Matrix': function (a, b) {
	      const d = lup(a);
	      return _lusolve(d.L, d.U, d.p, null, b);
	    },
	    'SparseMatrix, Array | Matrix, number, number': function (a, b, order, threshold) {
	      const d = slu(a, order, threshold);
	      return _lusolve(d.L, d.U, d.p, d.q, b);
	    },
	    'Object, Array | Matrix': function (d, b) {
	      return _lusolve(d.L, d.U, d.p, d.q, b);
	    }
	  });
	  function _toMatrix(a) {
	    if ((0, _is.isMatrix)(a)) {
	      return a;
	    }
	    if ((0, _is.isArray)(a)) {
	      return matrix(a);
	    }
	    throw new TypeError('Invalid Matrix LU decomposition');
	  }
	  function _lusolve(l, u, p, q, b) {
	    // verify decomposition
	    l = _toMatrix(l);
	    u = _toMatrix(u);

	    // apply row permutations if needed (b is a DenseMatrix)
	    if (p) {
	      b = solveValidation(l, b, true);
	      b._data = (0, _csIpvec.csIpvec)(p, b._data);
	    }

	    // use forward substitution to resolve L * y = b
	    const y = lsolve(l, b);
	    // use backward substitution to resolve U * x = y
	    const x = usolve(u, y);

	    // apply column permutations if needed (x is a DenseMatrix)
	    if (q) {
	      x._data = (0, _csIpvec.csIpvec)(q, x._data);
	    }
	    return x;
	  }
	});
	return lusolve$1;
}

var polynomialRoot$1 = {};

var hasRequiredPolynomialRoot$1;

function requirePolynomialRoot$1 () {
	if (hasRequiredPolynomialRoot$1) return polynomialRoot$1;
	hasRequiredPolynomialRoot$1 = 1;

	Object.defineProperty(polynomialRoot$1, "__esModule", {
	  value: true
	});
	polynomialRoot$1.createPolynomialRoot = void 0;
	var _factory = requireFactory$b();
	const name = 'polynomialRoot';
	const dependencies = ['typed', 'isZero', 'equalScalar', 'add', 'subtract', 'multiply', 'divide', 'sqrt', 'unaryMinus', 'cbrt', 'typeOf', 'im', 're'];
	polynomialRoot$1.createPolynomialRoot = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isZero,
	    equalScalar,
	    add,
	    subtract,
	    multiply,
	    divide,
	    sqrt,
	    unaryMinus,
	    cbrt,
	    typeOf,
	    im,
	    re
	  } = _ref;
	  /**
	   * Finds the numerical values of the distinct roots of a polynomial with real or complex coefficients.
	   * Currently operates only on linear, quadratic, and cubic polynomials using the standard
	   * formulas for the roots.
	   *
	   * Syntax:
	   *
	   *     math.polynomialRoot(constant, linearCoeff, quadraticCoeff, cubicCoeff)
	   *
	   * Examples:
	   *     // linear
	   *     math.polynomialRoot(6, 3)                                        // [-2]
	   *     math.polynomialRoot(math.complex(6,3), 3)                        // [-2 - i]
	   *     math.polynomialRoot(math.complex(6,3), math.complex(2,1))        // [-3 + 0i]
	   *     // quadratic
	   *     math.polynomialRoot(2, -3, 1)                                    // [2, 1]
	   *     math.polynomialRoot(8, 8, 2)                                     // [-2]
	   *     math.polynomialRoot(-2, 0, 1)                                    // [1.4142135623730951, -1.4142135623730951]
	   *     math.polynomialRoot(2, -2, 1)                                    // [1 + i, 1 - i]
	   *     math.polynomialRoot(math.complex(1,3), math.complex(-3, -2), 1)  // [2 + i, 1 + i]
	   *     // cubic
	   *     math.polynomialRoot(-6, 11, -6, 1)                               // [1, 3, 2]
	   *     math.polynomialRoot(-8, 0, 0, 1)                                 // [-1 - 1.7320508075688774i, 2, -1 + 1.7320508075688774i]
	   *     math.polynomialRoot(0, 8, 8, 2)                                  // [0, -2]
	   *     math.polynomialRoot(1, 1, 1, 1)                                  // [-1 + 0i, 0 - i, 0 + i]
	   *
	   * See also:
	   *     cbrt, sqrt
	   *
	   * History:
	   *
	   *     v11.4  Created
	   *
	   * @param {... number | Complex} coeffs
	   *     The coefficients of the polynomial, starting with with the constant coefficent, followed
	   *     by the linear coefficient and subsequent coefficients of increasing powers.
	   * @return {Array} The distinct roots of the polynomial
	   */

	  return typed(name, {
	    'number|Complex, ...number|Complex': (constant, restCoeffs) => {
	      const coeffs = [constant, ...restCoeffs];
	      while (coeffs.length > 0 && isZero(coeffs[coeffs.length - 1])) {
	        coeffs.pop();
	      }
	      if (coeffs.length < 2) {
	        throw new RangeError(`Polynomial [${constant}, ${restCoeffs}] must have a non-zero non-constant coefficient`);
	      }
	      switch (coeffs.length) {
	        case 2:
	          // linear
	          return [unaryMinus(divide(coeffs[0], coeffs[1]))];
	        case 3:
	          {
	            // quadratic
	            const [c, b, a] = coeffs;
	            const denom = multiply(2, a);
	            const d1 = multiply(b, b);
	            const d2 = multiply(4, a, c);
	            if (equalScalar(d1, d2)) return [divide(unaryMinus(b), denom)];
	            const discriminant = sqrt(subtract(d1, d2));
	            return [divide(subtract(discriminant, b), denom), divide(subtract(unaryMinus(discriminant), b), denom)];
	          }
	        case 4:
	          {
	            // cubic, cf. https://en.wikipedia.org/wiki/Cubic_equation
	            const [d, c, b, a] = coeffs;
	            const denom = unaryMinus(multiply(3, a));
	            const D0_1 = multiply(b, b);
	            const D0_2 = multiply(3, a, c);
	            const D1_1 = add(multiply(2, b, b, b), multiply(27, a, a, d));
	            const D1_2 = multiply(9, a, b, c);
	            if (equalScalar(D0_1, D0_2) && equalScalar(D1_1, D1_2)) {
	              return [divide(b, denom)];
	            }
	            const Delta0 = subtract(D0_1, D0_2);
	            const Delta1 = subtract(D1_1, D1_2);
	            const discriminant1 = add(multiply(18, a, b, c, d), multiply(b, b, c, c));
	            const discriminant2 = add(multiply(4, b, b, b, d), multiply(4, a, c, c, c), multiply(27, a, a, d, d));
	            if (equalScalar(discriminant1, discriminant2)) {
	              return [divide(subtract(multiply(4, a, b, c), add(multiply(9, a, a, d), multiply(b, b, b))), multiply(a, Delta0)),
	              // simple root
	              divide(subtract(multiply(9, a, d), multiply(b, c)), multiply(2, Delta0)) // double root
	              ];
	            }
	            // OK, we have three distinct roots
	            let Ccubed;
	            if (equalScalar(D0_1, D0_2)) {
	              Ccubed = Delta1;
	            } else {
	              Ccubed = divide(add(Delta1, sqrt(subtract(multiply(Delta1, Delta1), multiply(4, Delta0, Delta0, Delta0)))), 2);
	            }
	            const allRoots = true;
	            const rawRoots = cbrt(Ccubed, allRoots).toArray().map(C => divide(add(b, C, divide(Delta0, C)), denom));
	            return rawRoots.map(r => {
	              if (typeOf(r) === 'Complex' && equalScalar(re(r), re(r) + im(r))) {
	                return re(r);
	              }
	              return r;
	            });
	          }
	        default:
	          throw new RangeError(`only implemented for cubic or lower-order polynomials, not ${coeffs}`);
	      }
	    }
	  });
	});
	return polynomialRoot$1;
}

var Help = {};

var hasRequiredHelp$2;

function requireHelp$2 () {
	if (hasRequiredHelp$2) return Help;
	hasRequiredHelp$2 = 1;

	Object.defineProperty(Help, "__esModule", {
	  value: true
	});
	Help.createHelpClass = void 0;
	var _is = requireIs();
	var _object = requireObject$3();
	var _string = requireString$2();
	var _factory = requireFactory$b();
	const name = 'Help';
	const dependencies = ['evaluate'];
	Help.createHelpClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    evaluate
	  } = _ref;
	  /**
	   * Documentation object
	   * @param {Object} doc  Object containing properties:
	   *                      {string} name
	   *                      {string} category
	   *                      {string} description
	   *                      {string[]} syntax
	   *                      {string[]} examples
	   *                      {string[]} seealso
	   * @constructor
	   */
	  function Help(doc) {
	    if (!(this instanceof Help)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    if (!doc) throw new Error('Argument "doc" missing');
	    this.doc = doc;
	  }

	  /**
	   * Attach type information
	   */
	  Help.prototype.type = 'Help';
	  Help.prototype.isHelp = true;

	  /**
	   * Generate a string representation of the Help object
	   * @return {string} Returns a string
	   * @private
	   */
	  Help.prototype.toString = function () {
	    const doc = this.doc || {};
	    let desc = '\n';
	    if (doc.name) {
	      desc += 'Name: ' + doc.name + '\n\n';
	    }
	    if (doc.category) {
	      desc += 'Category: ' + doc.category + '\n\n';
	    }
	    if (doc.description) {
	      desc += 'Description:\n    ' + doc.description + '\n\n';
	    }
	    if (doc.syntax) {
	      desc += 'Syntax:\n    ' + doc.syntax.join('\n    ') + '\n\n';
	    }
	    if (doc.examples) {
	      desc += 'Examples:\n';

	      // after evaluating the examples, we restore config in case the examples
	      // did change the config.
	      let configChanged = false;
	      const originalConfig = evaluate('config()');
	      const scope = {
	        config: newConfig => {
	          configChanged = true;
	          return evaluate('config(newConfig)', {
	            newConfig
	          });
	        }
	      };
	      for (let i = 0; i < doc.examples.length; i++) {
	        const expr = doc.examples[i];
	        desc += '    ' + expr + '\n';
	        let res;
	        try {
	          // note: res can be undefined when `expr` is an empty string
	          res = evaluate(expr, scope);
	        } catch (e) {
	          res = e;
	        }
	        if (res !== undefined && !(0, _is.isHelp)(res)) {
	          desc += '        ' + (0, _string.format)(res, {
	            precision: 14
	          }) + '\n';
	        }
	      }
	      desc += '\n';
	      if (configChanged) {
	        evaluate('config(originalConfig)', {
	          originalConfig
	        });
	      }
	    }
	    if (doc.mayThrow && doc.mayThrow.length) {
	      desc += 'Throws: ' + doc.mayThrow.join(', ') + '\n\n';
	    }
	    if (doc.seealso && doc.seealso.length) {
	      desc += 'See also: ' + doc.seealso.join(', ') + '\n';
	    }
	    return desc;
	  };

	  /**
	   * Export the help object to JSON
	   */
	  Help.prototype.toJSON = function () {
	    const obj = (0, _object.clone)(this.doc);
	    obj.mathjs = 'Help';
	    return obj;
	  };

	  /**
	   * Instantiate a Help object from a JSON object
	   * @param {Object} json
	   * @returns {Help} Returns a new Help object
	   */
	  Help.fromJSON = function (json) {
	    const doc = {};
	    Object.keys(json).filter(prop => prop !== 'mathjs').forEach(prop => {
	      doc[prop] = json[prop];
	    });
	    return new Help(doc);
	  };

	  /**
	   * Returns a string representation of the Help object
	   */
	  Help.prototype.valueOf = Help.prototype.toString;
	  return Help;
	}, {
	  isClass: true
	});
	return Help;
}

var Chain = {};

var hasRequiredChain$1;

function requireChain$1 () {
	if (hasRequiredChain$1) return Chain;
	hasRequiredChain$1 = 1;

	Object.defineProperty(Chain, "__esModule", {
	  value: true
	});
	Chain.createChainClass = void 0;
	var _is = requireIs();
	var _string = requireString$2();
	var _object = requireObject$3();
	var _factory = requireFactory$b();
	const name = 'Chain';
	const dependencies = ['?on', 'math', 'typed'];
	Chain.createChainClass = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    on,
	    math,
	    typed
	  } = _ref;
	  /**
	   * @constructor Chain
	   * Wrap any value in a chain, allowing to perform chained operations on
	   * the value.
	   *
	   * All methods available in the math.js library can be called upon the chain,
	   * and then will be evaluated with the value itself as first argument.
	   * The chain can be closed by executing chain.done(), which will return
	   * the final value.
	   *
	   * The Chain has a number of special functions:
	   * - done()             Finalize the chained operation and return the
	   *                      chain's value.
	   * - valueOf()          The same as done()
	   * - toString()         Returns a string representation of the chain's value.
	   *
	   * @param {*} [value]
	   */
	  function Chain(value) {
	    if (!(this instanceof Chain)) {
	      throw new SyntaxError('Constructor must be called with the new operator');
	    }
	    if ((0, _is.isChain)(value)) {
	      this.value = value.value;
	    } else {
	      this.value = value;
	    }
	  }

	  /**
	   * Attach type information
	   */
	  Chain.prototype.type = 'Chain';
	  Chain.prototype.isChain = true;

	  /**
	   * Close the chain. Returns the final value.
	   * Does the same as method valueOf()
	   * @returns {*} value
	   */
	  Chain.prototype.done = function () {
	    return this.value;
	  };

	  /**
	   * Close the chain. Returns the final value.
	   * Does the same as method done()
	   * @returns {*} value
	   */
	  Chain.prototype.valueOf = function () {
	    return this.value;
	  };

	  /**
	   * Get a string representation of the value in the chain
	   * @returns {string}
	   */
	  Chain.prototype.toString = function () {
	    return (0, _string.format)(this.value);
	  };

	  /**
	   * Get a JSON representation of the chain
	   * @returns {Object}
	   */
	  Chain.prototype.toJSON = function () {
	    return {
	      mathjs: 'Chain',
	      value: this.value
	    };
	  };

	  /**
	   * Instantiate a Chain from its JSON representation
	   * @param {Object} json  An object structured like
	   *                       `{"mathjs": "Chain", value: ...}`,
	   *                       where mathjs is optional
	   * @returns {Chain}
	   */
	  Chain.fromJSON = function (json) {
	    return new Chain(json.value);
	  };

	  /**
	   * Create a proxy method for the chain
	   * @param {string} name
	   * @param {Function} fn      The function to be proxied
	   *                           If fn is no function, it is silently ignored.
	   * @private
	   */
	  function createProxy(name, fn) {
	    if (typeof fn === 'function') {
	      Chain.prototype[name] = chainify(fn);
	    }
	  }

	  /**
	   * Create a proxy method for the chain
	   * @param {string} name
	   * @param {function} resolver   The function resolving with the
	   *                              function to be proxied
	   * @private
	   */
	  function createLazyProxy(name, resolver) {
	    (0, _object.lazy)(Chain.prototype, name, function outerResolver() {
	      const fn = resolver();
	      if (typeof fn === 'function') {
	        return chainify(fn);
	      }
	      return undefined; // if not a function, ignore
	    });
	  }

	  /**
	   * Make a function chainable
	   * @param {function} fn
	   * @return {Function} chain function
	   * @private
	   */
	  function chainify(fn) {
	    return function () {
	      // Here, `this` will be the context of a Chain instance
	      if (arguments.length === 0) {
	        return new Chain(fn(this.value));
	      }
	      const args = [this.value];
	      for (let i = 0; i < arguments.length; i++) {
	        args[i + 1] = arguments[i];
	      }
	      if (typed.isTypedFunction(fn)) {
	        const sigObject = typed.resolve(fn, args);
	        // We want to detect if a rest parameter has matched across the
	        // value in the chain and the current arguments of this call.
	        // That is the case if and only if the matching signature has
	        // exactly one parameter (which then must be a rest parameter
	        // as it is matching at least two actual arguments).
	        if (sigObject.params.length === 1) {
	          throw new Error('chain function ' + fn.name + ' cannot match rest parameter between chain value and additional arguments.');
	        }
	        return new Chain(sigObject.implementation.apply(fn, args));
	      }
	      return new Chain(fn.apply(fn, args));
	    };
	  }

	  /**
	   * Create a proxy for a single method, or an object with multiple methods.
	   * Example usage:
	   *
	   *   Chain.createProxy('add', function add (x, y) {...})
	   *   Chain.createProxy({
	   *     add:      function add (x, y) {...},
	   *     subtract: function subtract (x, y) {...}
	   *   }
	   *
	   * @param {string | Object} arg0   A name (string), or an object with
	   *                                 functions
	   * @param {*} [arg1]               A function, when arg0 is a name
	   */
	  Chain.createProxy = function (arg0, arg1) {
	    if (typeof arg0 === 'string') {
	      // createProxy(name, value)
	      createProxy(arg0, arg1);
	    } else {
	      // createProxy(values)
	      for (const name in arg0) {
	        if ((0, _object.hasOwnProperty)(arg0, name) && excludedNames[name] === undefined) {
	          createLazyProxy(name, () => arg0[name]);
	        }
	      }
	    }
	  };
	  const excludedNames = {
	    expression: true,
	    docs: true,
	    type: true,
	    classes: true,
	    json: true,
	    error: true,
	    isChain: true // conflicts with the property isChain of a Chain instance
	  };

	  // create proxy for everything that is in math.js
	  Chain.createProxy(math);

	  // register on the import event, automatically add a proxy for every imported function.
	  if (on) {
	    on('import', function (name, resolver, path) {
	      if (!path) {
	        // an imported function (not a data type or something special)
	        createLazyProxy(name, resolver);
	      }
	    });
	  }
	  return Chain;
	}, {
	  isClass: true
	});
	return Chain;
}

var help$1 = {};

var embeddedDocs = {};

var e = {};

var hasRequiredE;

function requireE () {
	if (hasRequiredE) return e;
	hasRequiredE = 1;

	Object.defineProperty(e, "__esModule", {
	  value: true
	});
	e.eDocs = void 0;
	e.eDocs = {
	  name: 'e',
	  category: 'Constants',
	  syntax: ['e'],
	  description: 'Euler\'s number, the base of the natural logarithm. Approximately equal to 2.71828',
	  examples: ['e', 'e ^ 2', 'exp(2)', 'log(e)'],
	  seealso: ['exp']
	};
	return e;
}

var _false = {};

var hasRequired_false;

function require_false () {
	if (hasRequired_false) return _false;
	hasRequired_false = 1;

	Object.defineProperty(_false, "__esModule", {
	  value: true
	});
	_false.falseDocs = void 0;
	_false.falseDocs = {
	  name: 'false',
	  category: 'Constants',
	  syntax: ['false'],
	  description: 'Boolean value false',
	  examples: ['false'],
	  seealso: ['true']
	};
	return _false;
}

var i = {};

var hasRequiredI;

function requireI () {
	if (hasRequiredI) return i;
	hasRequiredI = 1;

	Object.defineProperty(i, "__esModule", {
	  value: true
	});
	i.iDocs = void 0;
	i.iDocs = {
	  name: 'i',
	  category: 'Constants',
	  syntax: ['i'],
	  description: 'Imaginary unit, defined as i*i=-1. A complex number is described as a + b*i, where a is the real part, and b is the imaginary part.',
	  examples: ['i', 'i * i', 'sqrt(-1)'],
	  seealso: []
	};
	return i;
}

var _Infinity = {};

var hasRequired_Infinity;

function require_Infinity () {
	if (hasRequired_Infinity) return _Infinity;
	hasRequired_Infinity = 1;

	Object.defineProperty(_Infinity, "__esModule", {
	  value: true
	});
	_Infinity.InfinityDocs = void 0;
	_Infinity.InfinityDocs = {
	  name: 'Infinity',
	  category: 'Constants',
	  syntax: ['Infinity'],
	  description: 'Infinity, a number which is larger than the maximum number that can be handled by a floating point number.',
	  examples: ['Infinity', '1 / 0'],
	  seealso: []
	};
	return _Infinity;
}

var LN10 = {};

var hasRequiredLN10;

function requireLN10 () {
	if (hasRequiredLN10) return LN10;
	hasRequiredLN10 = 1;

	Object.defineProperty(LN10, "__esModule", {
	  value: true
	});
	LN10.LN10Docs = void 0;
	LN10.LN10Docs = {
	  name: 'LN10',
	  category: 'Constants',
	  syntax: ['LN10'],
	  description: 'Returns the natural logarithm of 10, approximately equal to 2.302',
	  examples: ['LN10', 'log(10)'],
	  seealso: []
	};
	return LN10;
}

var LN2 = {};

var hasRequiredLN2;

function requireLN2 () {
	if (hasRequiredLN2) return LN2;
	hasRequiredLN2 = 1;

	Object.defineProperty(LN2, "__esModule", {
	  value: true
	});
	LN2.LN2Docs = void 0;
	LN2.LN2Docs = {
	  name: 'LN2',
	  category: 'Constants',
	  syntax: ['LN2'],
	  description: 'Returns the natural logarithm of 2, approximately equal to 0.693',
	  examples: ['LN2', 'log(2)'],
	  seealso: []
	};
	return LN2;
}

var LOG10E = {};

var hasRequiredLOG10E;

function requireLOG10E () {
	if (hasRequiredLOG10E) return LOG10E;
	hasRequiredLOG10E = 1;

	Object.defineProperty(LOG10E, "__esModule", {
	  value: true
	});
	LOG10E.LOG10EDocs = void 0;
	LOG10E.LOG10EDocs = {
	  name: 'LOG10E',
	  category: 'Constants',
	  syntax: ['LOG10E'],
	  description: 'Returns the base-10 logarithm of E, approximately equal to 0.434',
	  examples: ['LOG10E', 'log(e, 10)'],
	  seealso: []
	};
	return LOG10E;
}

var LOG2E = {};

var hasRequiredLOG2E;

function requireLOG2E () {
	if (hasRequiredLOG2E) return LOG2E;
	hasRequiredLOG2E = 1;

	Object.defineProperty(LOG2E, "__esModule", {
	  value: true
	});
	LOG2E.LOG2EDocs = void 0;
	LOG2E.LOG2EDocs = {
	  name: 'LOG2E',
	  category: 'Constants',
	  syntax: ['LOG2E'],
	  description: 'Returns the base-2 logarithm of E, approximately equal to 1.442',
	  examples: ['LOG2E', 'log(e, 2)'],
	  seealso: []
	};
	return LOG2E;
}

var _NaN = {};

var hasRequired_NaN;

function require_NaN () {
	if (hasRequired_NaN) return _NaN;
	hasRequired_NaN = 1;

	Object.defineProperty(_NaN, "__esModule", {
	  value: true
	});
	_NaN.NaNDocs = void 0;
	_NaN.NaNDocs = {
	  name: 'NaN',
	  category: 'Constants',
	  syntax: ['NaN'],
	  description: 'Not a number',
	  examples: ['NaN', '0 / 0'],
	  seealso: []
	};
	return _NaN;
}

var _null = {};

var hasRequired_null;

function require_null () {
	if (hasRequired_null) return _null;
	hasRequired_null = 1;

	Object.defineProperty(_null, "__esModule", {
	  value: true
	});
	_null.nullDocs = void 0;
	_null.nullDocs = {
	  name: 'null',
	  category: 'Constants',
	  syntax: ['null'],
	  description: 'Value null',
	  examples: ['null'],
	  seealso: ['true', 'false']
	};
	return _null;
}

var phi = {};

var hasRequiredPhi;

function requirePhi () {
	if (hasRequiredPhi) return phi;
	hasRequiredPhi = 1;

	Object.defineProperty(phi, "__esModule", {
	  value: true
	});
	phi.phiDocs = void 0;
	phi.phiDocs = {
	  name: 'phi',
	  category: 'Constants',
	  syntax: ['phi'],
	  description: 'Phi is the golden ratio. Two quantities are in the golden ratio if their ratio is the same as the ratio of their sum to the larger of the two quantities. Phi is defined as `(1 + sqrt(5)) / 2` and is approximately 1.618034...',
	  examples: ['phi'],
	  seealso: []
	};
	return phi;
}

var pi = {};

var hasRequiredPi;

function requirePi () {
	if (hasRequiredPi) return pi;
	hasRequiredPi = 1;

	Object.defineProperty(pi, "__esModule", {
	  value: true
	});
	pi.piDocs = void 0;
	pi.piDocs = {
	  name: 'pi',
	  category: 'Constants',
	  syntax: ['pi'],
	  description: 'The number pi is a mathematical constant that is the ratio of a circle\'s circumference to its diameter, and is approximately equal to 3.14159',
	  examples: ['pi', 'sin(pi/2)'],
	  seealso: ['tau']
	};
	return pi;
}

var SQRT1_2 = {};

var hasRequiredSQRT1_2;

function requireSQRT1_2 () {
	if (hasRequiredSQRT1_2) return SQRT1_2;
	hasRequiredSQRT1_2 = 1;

	Object.defineProperty(SQRT1_2, "__esModule", {
	  value: true
	});
	SQRT1_2.SQRT12Docs = void 0;
	SQRT1_2.SQRT12Docs = {
	  name: 'SQRT1_2',
	  category: 'Constants',
	  syntax: ['SQRT1_2'],
	  description: 'Returns the square root of 1/2, approximately equal to 0.707',
	  examples: ['SQRT1_2', 'sqrt(1/2)'],
	  seealso: []
	};
	return SQRT1_2;
}

var SQRT2 = {};

var hasRequiredSQRT2;

function requireSQRT2 () {
	if (hasRequiredSQRT2) return SQRT2;
	hasRequiredSQRT2 = 1;

	Object.defineProperty(SQRT2, "__esModule", {
	  value: true
	});
	SQRT2.SQRT2Docs = void 0;
	SQRT2.SQRT2Docs = {
	  name: 'SQRT2',
	  category: 'Constants',
	  syntax: ['SQRT2'],
	  description: 'Returns the square root of 2, approximately equal to 1.414',
	  examples: ['SQRT2', 'sqrt(2)'],
	  seealso: []
	};
	return SQRT2;
}

var tau = {};

var hasRequiredTau;

function requireTau () {
	if (hasRequiredTau) return tau;
	hasRequiredTau = 1;

	Object.defineProperty(tau, "__esModule", {
	  value: true
	});
	tau.tauDocs = void 0;
	tau.tauDocs = {
	  name: 'tau',
	  category: 'Constants',
	  syntax: ['tau'],
	  description: 'Tau is the ratio constant of a circle\'s circumference to radius, equal to 2 * pi, approximately 6.2832.',
	  examples: ['tau', '2 * pi'],
	  seealso: ['pi']
	};
	return tau;
}

var _true = {};

var hasRequired_true;

function require_true () {
	if (hasRequired_true) return _true;
	hasRequired_true = 1;

	Object.defineProperty(_true, "__esModule", {
	  value: true
	});
	_true.trueDocs = void 0;
	_true.trueDocs = {
	  name: 'true',
	  category: 'Constants',
	  syntax: ['true'],
	  description: 'Boolean value true',
	  examples: ['true'],
	  seealso: ['false']
	};
	return _true;
}

var version$2 = {};

var hasRequiredVersion$1;

function requireVersion$1 () {
	if (hasRequiredVersion$1) return version$2;
	hasRequiredVersion$1 = 1;

	Object.defineProperty(version$2, "__esModule", {
	  value: true
	});
	version$2.versionDocs = void 0;
	version$2.versionDocs = {
	  name: 'version',
	  category: 'Constants',
	  syntax: ['version'],
	  description: 'A string with the version number of math.js',
	  examples: ['version'],
	  seealso: []
	};
	return version$2;
}

var bignumber = {};

var hasRequiredBignumber;

function requireBignumber () {
	if (hasRequiredBignumber) return bignumber;
	hasRequiredBignumber = 1;

	Object.defineProperty(bignumber, "__esModule", {
	  value: true
	});
	bignumber.bignumberDocs = void 0;
	bignumber.bignumberDocs = {
	  name: 'bignumber',
	  category: 'Construction',
	  syntax: ['bignumber(x)'],
	  description: 'Create a big number from a number or string.',
	  examples: ['0.1 + 0.2', 'bignumber(0.1) + bignumber(0.2)', 'bignumber("7.2")', 'bignumber("7.2e500")', 'bignumber([0.1, 0.2, 0.3])'],
	  seealso: ['boolean', 'bigint', 'complex', 'fraction', 'index', 'matrix', 'string', 'unit']
	};
	return bignumber;
}

var bigint = {};

var hasRequiredBigint;

function requireBigint () {
	if (hasRequiredBigint) return bigint;
	hasRequiredBigint = 1;

	Object.defineProperty(bigint, "__esModule", {
	  value: true
	});
	bigint.bigintDocs = void 0;
	bigint.bigintDocs = {
	  name: 'bigint',
	  category: 'Construction',
	  syntax: ['bigint(x)'],
	  description: 'Create a bigint, an integer with an arbitrary number of digits, from a number or string.',
	  examples: ['123123123123123123 # a large number will lose digits', 'bigint("123123123123123123")', 'bignumber(["1", "3", "5"])'],
	  seealso: ['boolean', 'bignumber', 'number', 'complex', 'fraction', 'index', 'matrix', 'string', 'unit']
	};
	return bigint;
}

var boolean = {};

var hasRequiredBoolean;

function requireBoolean () {
	if (hasRequiredBoolean) return boolean;
	hasRequiredBoolean = 1;

	Object.defineProperty(boolean, "__esModule", {
	  value: true
	});
	boolean.booleanDocs = void 0;
	boolean.booleanDocs = {
	  name: 'boolean',
	  category: 'Construction',
	  syntax: ['x', 'boolean(x)'],
	  description: 'Convert a string or number into a boolean.',
	  examples: ['boolean(0)', 'boolean(1)', 'boolean(3)', 'boolean("true")', 'boolean("false")', 'boolean([1, 0, 1, 1])'],
	  seealso: ['bignumber', 'complex', 'index', 'matrix', 'number', 'string', 'unit']
	};
	return boolean;
}

var complex = {};

var hasRequiredComplex;

function requireComplex () {
	if (hasRequiredComplex) return complex;
	hasRequiredComplex = 1;

	Object.defineProperty(complex, "__esModule", {
	  value: true
	});
	complex.complexDocs = void 0;
	complex.complexDocs = {
	  name: 'complex',
	  category: 'Construction',
	  syntax: ['complex()', 'complex(re, im)', 'complex(string)'],
	  description: 'Create a complex number.',
	  examples: ['complex()', 'complex(2, 3)', 'complex("7 - 2i")'],
	  seealso: ['bignumber', 'boolean', 'index', 'matrix', 'number', 'string', 'unit']
	};
	return complex;
}

var createUnit = {};

var hasRequiredCreateUnit;

function requireCreateUnit () {
	if (hasRequiredCreateUnit) return createUnit;
	hasRequiredCreateUnit = 1;

	Object.defineProperty(createUnit, "__esModule", {
	  value: true
	});
	createUnit.createUnitDocs = void 0;
	createUnit.createUnitDocs = {
	  name: 'createUnit',
	  category: 'Construction',
	  syntax: ['createUnit(definitions)', 'createUnit(name, definition)'],
	  description: 'Create a user-defined unit and register it with the Unit type.',
	  examples: ['createUnit("foo")', 'createUnit("knot", {definition: "0.514444444 m/s", aliases: ["knots", "kt", "kts"]})', 'createUnit("mph", "1 mile/hour")'],
	  seealso: ['unit', 'splitUnit']
	};
	return createUnit;
}

var fraction = {};

var hasRequiredFraction;

function requireFraction () {
	if (hasRequiredFraction) return fraction;
	hasRequiredFraction = 1;

	Object.defineProperty(fraction, "__esModule", {
	  value: true
	});
	fraction.fractionDocs = void 0;
	fraction.fractionDocs = {
	  name: 'fraction',
	  category: 'Construction',
	  syntax: ['fraction(num)', 'fraction(matrix)', 'fraction(num,den)', 'fraction({n: num, d: den})'],
	  description: 'Create a fraction from a number or from integer numerator and denominator.',
	  examples: ['fraction(0.125)', 'fraction(1, 3) + fraction(2, 5)', 'fraction({n: 333, d: 53})', 'fraction([sqrt(9), sqrt(10), sqrt(11)])'],
	  seealso: ['bignumber', 'boolean', 'complex', 'index', 'matrix', 'string', 'unit']
	};
	return fraction;
}

var construction = {};

var hasRequiredConstruction;

function requireConstruction () {
	if (hasRequiredConstruction) return construction;
	hasRequiredConstruction = 1;

	Object.defineProperty(construction, "__esModule", {
	  value: true
	});
	construction.indexDocs = void 0;
	construction.indexDocs = {
	  name: 'index',
	  category: 'Construction',
	  syntax: ['[start]', '[start:end]', '[start:step:end]', '[start1, start 2, ...]', '[start1:end1, start2:end2, ...]', '[start1:step1:end1, start2:step2:end2, ...]'],
	  description: 'Create an index to get or replace a subset of a matrix',
	  examples: ['A = [1, 2, 3; 4, 5, 6]', 'A[1, :]', 'A[1, 2] = 50', 'A[1:2, 1:2] = 1', 'B = [1, 2, 3]', 'B[B>1 and B<3]'],
	  seealso: ['bignumber', 'boolean', 'complex', 'matrix', 'number', 'range', 'string', 'unit']
	};
	return construction;
}

var matrix$1 = {};

var hasRequiredMatrix;

function requireMatrix () {
	if (hasRequiredMatrix) return matrix$1;
	hasRequiredMatrix = 1;

	Object.defineProperty(matrix$1, "__esModule", {
	  value: true
	});
	matrix$1.matrixDocs = void 0;
	matrix$1.matrixDocs = {
	  name: 'matrix',
	  category: 'Construction',
	  syntax: ['[]', '[a1, b1, ...; a2, b2, ...]', 'matrix()', 'matrix("dense")', 'matrix([...])'],
	  description: 'Create a matrix.',
	  examples: ['[]', '[1, 2, 3]', '[1, 2, 3; 4, 5, 6]', 'matrix()', 'matrix([3, 4])', 'matrix([3, 4; 5, 6], "sparse")', 'matrix([3, 4; 5, 6], "sparse", "number")'],
	  seealso: ['bignumber', 'boolean', 'complex', 'index', 'number', 'string', 'unit', 'sparse']
	};
	return matrix$1;
}

var number = {};

var hasRequiredNumber;

function requireNumber () {
	if (hasRequiredNumber) return number;
	hasRequiredNumber = 1;

	Object.defineProperty(number, "__esModule", {
	  value: true
	});
	number.numberDocs = void 0;
	number.numberDocs = {
	  name: 'number',
	  category: 'Construction',
	  syntax: ['x', 'number(x)', 'number(unit, valuelessUnit)'],
	  description: 'Create a number or convert a string or boolean into a number.',
	  examples: ['2', '2e3', '4.05', 'number(2)', 'number("7.2")', 'number(true)', 'number([true, false, true, true])', 'number(unit("52cm"), "m")'],
	  seealso: ['bignumber', 'bigint', 'boolean', 'complex', 'fraction', 'index', 'matrix', 'string', 'unit']
	};
	return number;
}

var sparse = {};

var hasRequiredSparse;

function requireSparse () {
	if (hasRequiredSparse) return sparse;
	hasRequiredSparse = 1;

	Object.defineProperty(sparse, "__esModule", {
	  value: true
	});
	sparse.sparseDocs = void 0;
	sparse.sparseDocs = {
	  name: 'sparse',
	  category: 'Construction',
	  syntax: ['sparse()', 'sparse([a1, b1, ...; a1, b2, ...])', 'sparse([a1, b1, ...; a1, b2, ...], "number")'],
	  description: 'Create a sparse matrix.',
	  examples: ['sparse()', 'sparse([3, 4; 5, 6])', 'sparse([3, 0; 5, 0], "number")'],
	  seealso: ['bignumber', 'boolean', 'complex', 'index', 'number', 'string', 'unit', 'matrix']
	};
	return sparse;
}

var splitUnit = {};

var hasRequiredSplitUnit;

function requireSplitUnit () {
	if (hasRequiredSplitUnit) return splitUnit;
	hasRequiredSplitUnit = 1;

	Object.defineProperty(splitUnit, "__esModule", {
	  value: true
	});
	splitUnit.splitUnitDocs = void 0;
	splitUnit.splitUnitDocs = {
	  name: 'splitUnit',
	  category: 'Construction',
	  syntax: ['splitUnit(unit: Unit, parts: Unit[])'],
	  description: 'Split a unit in an array of units whose sum is equal to the original unit.',
	  examples: ['splitUnit(1 m, ["feet", "inch"])'],
	  seealso: ['unit', 'createUnit']
	};
	return splitUnit;
}

var string = {};

var hasRequiredString;

function requireString () {
	if (hasRequiredString) return string;
	hasRequiredString = 1;

	Object.defineProperty(string, "__esModule", {
	  value: true
	});
	string.stringDocs = void 0;
	string.stringDocs = {
	  name: 'string',
	  category: 'Construction',
	  syntax: ['"text"', 'string(x)'],
	  description: 'Create a string or convert a value to a string',
	  examples: ['"Hello World!"', 'string(4.2)', 'string(3 + 2i)'],
	  seealso: ['bignumber', 'boolean', 'complex', 'index', 'matrix', 'number', 'unit']
	};
	return string;
}

var unit = {};

var hasRequiredUnit;

function requireUnit () {
	if (hasRequiredUnit) return unit;
	hasRequiredUnit = 1;

	Object.defineProperty(unit, "__esModule", {
	  value: true
	});
	unit.unitDocs = void 0;
	unit.unitDocs = {
	  name: 'unit',
	  category: 'Construction',
	  syntax: ['value unit', 'unit(value, unit)', 'unit(string)'],
	  description: 'Create a unit.',
	  examples: ['5.5 mm', '3 inch', 'unit(7.1, "kilogram")', 'unit("23 deg")'],
	  seealso: ['bignumber', 'boolean', 'complex', 'index', 'matrix', 'number', 'string']
	};
	return unit;
}

var config$1 = {};

var hasRequiredConfig;

function requireConfig () {
	if (hasRequiredConfig) return config$1;
	hasRequiredConfig = 1;

	Object.defineProperty(config$1, "__esModule", {
	  value: true
	});
	config$1.configDocs = void 0;
	config$1.configDocs = {
	  name: 'config',
	  category: 'Core',
	  syntax: ['config()', 'config(options)'],
	  description: 'Get configuration or change configuration.',
	  examples: ['config()', '1/3 + 1/4', 'config({number: "Fraction"})', '1/3 + 1/4'],
	  seealso: []
	};
	return config$1;
}

var _import$1 = {};

var hasRequired_import$1;

function require_import$1 () {
	if (hasRequired_import$1) return _import$1;
	hasRequired_import$1 = 1;

	Object.defineProperty(_import$1, "__esModule", {
	  value: true
	});
	_import$1.importDocs = void 0;
	_import$1.importDocs = {
	  name: 'import',
	  category: 'Core',
	  syntax: ['import(functions)', 'import(functions, options)'],
	  description: 'Import functions or constants from an object.',
	  examples: ['import({myFn: f(x)=x^2, myConstant: 32 })', 'myFn(2)', 'myConstant'],
	  seealso: []
	};
	return _import$1;
}

var typed = {};

var hasRequiredTyped;

function requireTyped () {
	if (hasRequiredTyped) return typed;
	hasRequiredTyped = 1;

	Object.defineProperty(typed, "__esModule", {
	  value: true
	});
	typed.typedDocs = void 0;
	typed.typedDocs = {
	  name: 'typed',
	  category: 'Core',
	  syntax: ['typed(signatures)', 'typed(name, signatures)'],
	  description: 'Create a typed function.',
	  examples: ['double = typed({ "number": f(x)=x+x, "string": f(x)=concat(x,x) })', 'double(2)', 'double("hello")'],
	  seealso: []
	};
	return typed;
}

var derivative$1 = {};

var hasRequiredDerivative$1;

function requireDerivative$1 () {
	if (hasRequiredDerivative$1) return derivative$1;
	hasRequiredDerivative$1 = 1;

	Object.defineProperty(derivative$1, "__esModule", {
	  value: true
	});
	derivative$1.derivativeDocs = void 0;
	derivative$1.derivativeDocs = {
	  name: 'derivative',
	  category: 'Algebra',
	  syntax: ['derivative(expr, variable)', 'derivative(expr, variable, {simplify: boolean})'],
	  description: 'Takes the derivative of an expression expressed in parser Nodes. The derivative will be taken over the supplied variable in the second parameter. If there are multiple variables in the expression, it will return a partial derivative.',
	  examples: ['derivative("2x^3", "x")', 'derivative("2x^3", "x", {simplify: false})', 'derivative("2x^2 + 3x + 4", "x")', 'derivative("sin(2x)", "x")', 'f = parse("x^2 + x")', 'x = parse("x")', 'df = derivative(f, x)', 'df.evaluate({x: 3})'],
	  seealso: ['simplify', 'parse', 'evaluate']
	};
	return derivative$1;
}

var leafCount$1 = {};

var hasRequiredLeafCount$1;

function requireLeafCount$1 () {
	if (hasRequiredLeafCount$1) return leafCount$1;
	hasRequiredLeafCount$1 = 1;

	Object.defineProperty(leafCount$1, "__esModule", {
	  value: true
	});
	leafCount$1.leafCountDocs = void 0;
	leafCount$1.leafCountDocs = {
	  name: 'leafCount',
	  category: 'Algebra',
	  syntax: ['leafCount(expr)'],
	  description: 'Computes the number of leaves in the parse tree of the given expression',
	  examples: ['leafCount("e^(i*pi)-1")', 'leafCount(parse("{a: 22/7, b: 10^(1/2)}"))'],
	  seealso: ['simplify']
	};
	return leafCount$1;
}

var lsolve = {};

var hasRequiredLsolve;

function requireLsolve () {
	if (hasRequiredLsolve) return lsolve;
	hasRequiredLsolve = 1;

	Object.defineProperty(lsolve, "__esModule", {
	  value: true
	});
	lsolve.lsolveDocs = void 0;
	lsolve.lsolveDocs = {
	  name: 'lsolve',
	  category: 'Algebra',
	  syntax: ['x=lsolve(L, b)'],
	  description: 'Finds one solution of the linear system L * x = b where L is an [n x n] lower triangular matrix and b is a [n] column vector.',
	  examples: ['a = [-2, 3; 2, 1]', 'b = [11, 9]', 'x = lsolve(a, b)'],
	  seealso: ['lsolveAll', 'lup', 'lusolve', 'usolve', 'matrix', 'sparse']
	};
	return lsolve;
}

var lsolveAll = {};

var hasRequiredLsolveAll;

function requireLsolveAll () {
	if (hasRequiredLsolveAll) return lsolveAll;
	hasRequiredLsolveAll = 1;

	Object.defineProperty(lsolveAll, "__esModule", {
	  value: true
	});
	lsolveAll.lsolveAllDocs = void 0;
	lsolveAll.lsolveAllDocs = {
	  name: 'lsolveAll',
	  category: 'Algebra',
	  syntax: ['x=lsolveAll(L, b)'],
	  description: 'Finds all solutions of the linear system L * x = b where L is an [n x n] lower triangular matrix and b is a [n] column vector.',
	  examples: ['a = [-2, 3; 2, 1]', 'b = [11, 9]', 'x = lsolve(a, b)'],
	  seealso: ['lsolve', 'lup', 'lusolve', 'usolve', 'matrix', 'sparse']
	};
	return lsolveAll;
}

var lup = {};

var hasRequiredLup;

function requireLup () {
	if (hasRequiredLup) return lup;
	hasRequiredLup = 1;

	Object.defineProperty(lup, "__esModule", {
	  value: true
	});
	lup.lupDocs = void 0;
	lup.lupDocs = {
	  name: 'lup',
	  category: 'Algebra',
	  syntax: ['lup(m)'],
	  description: 'Calculate the Matrix LU decomposition with partial pivoting. Matrix A is decomposed in three matrices (L, U, P) where P * A = L * U',
	  examples: ['lup([[2, 1], [1, 4]])', 'lup(matrix([[2, 1], [1, 4]]))', 'lup(sparse([[2, 1], [1, 4]]))'],
	  seealso: ['lusolve', 'lsolve', 'usolve', 'matrix', 'sparse', 'slu', 'qr']
	};
	return lup;
}

var lusolve = {};

var hasRequiredLusolve;

function requireLusolve () {
	if (hasRequiredLusolve) return lusolve;
	hasRequiredLusolve = 1;

	Object.defineProperty(lusolve, "__esModule", {
	  value: true
	});
	lusolve.lusolveDocs = void 0;
	lusolve.lusolveDocs = {
	  name: 'lusolve',
	  category: 'Algebra',
	  syntax: ['x=lusolve(A, b)', 'x=lusolve(lu, b)'],
	  description: 'Solves the linear system A * x = b where A is an [n x n] matrix and b is a [n] column vector.',
	  examples: ['a = [-2, 3; 2, 1]', 'b = [11, 9]', 'x = lusolve(a, b)'],
	  seealso: ['lup', 'slu', 'lsolve', 'usolve', 'matrix', 'sparse']
	};
	return lusolve;
}

var polynomialRoot = {};

var hasRequiredPolynomialRoot;

function requirePolynomialRoot () {
	if (hasRequiredPolynomialRoot) return polynomialRoot;
	hasRequiredPolynomialRoot = 1;

	Object.defineProperty(polynomialRoot, "__esModule", {
	  value: true
	});
	polynomialRoot.polynomialRootDocs = void 0;
	polynomialRoot.polynomialRootDocs = {
	  name: 'polynomialRoot',
	  category: 'Algebra',
	  syntax: ['x=polynomialRoot(-6, 3)', 'x=polynomialRoot(4, -4, 1)', 'x=polynomialRoot(-8, 12, -6, 1)'],
	  description: 'Finds the roots of a univariate polynomial given by its coefficients starting from constant, linear, and so on, increasing in degree.',
	  examples: ['a = polynomialRoot(-6, 11, -6, 1)'],
	  seealso: ['cbrt', 'sqrt']
	};
	return polynomialRoot;
}

var qr = {};

var hasRequiredQr;

function requireQr () {
	if (hasRequiredQr) return qr;
	hasRequiredQr = 1;

	Object.defineProperty(qr, "__esModule", {
	  value: true
	});
	qr.qrDocs = void 0;
	qr.qrDocs = {
	  name: 'qr',
	  category: 'Algebra',
	  syntax: ['qr(A)'],
	  description: 'Calculates the Matrix QR decomposition. Matrix `A` is decomposed in two matrices (`Q`, `R`) where `Q` is an orthogonal matrix and `R` is an upper triangular matrix.',
	  examples: ['qr([[1, -1,  4], [1,  4, -2], [1,  4,  2], [1,  -1, 0]])'],
	  seealso: ['lup', 'slu', 'matrix']
	};
	return qr;
}

var rationalize$1 = {};

var hasRequiredRationalize$1;

function requireRationalize$1 () {
	if (hasRequiredRationalize$1) return rationalize$1;
	hasRequiredRationalize$1 = 1;

	Object.defineProperty(rationalize$1, "__esModule", {
	  value: true
	});
	rationalize$1.rationalizeDocs = void 0;
	rationalize$1.rationalizeDocs = {
	  name: 'rationalize',
	  category: 'Algebra',
	  syntax: ['rationalize(expr)', 'rationalize(expr, scope)', 'rationalize(expr, scope, detailed)'],
	  description: 'Transform a rationalizable expression in a rational fraction. If rational fraction is one variable polynomial then converts the numerator and denominator in canonical form, with decreasing exponents, returning the coefficients of numerator.',
	  examples: ['rationalize("2x/y - y/(x+1)")', 'rationalize("2x/y - y/(x+1)", true)'],
	  seealso: ['simplify']
	};
	return rationalize$1;
}

var resolve$1 = {};

var hasRequiredResolve$1;

function requireResolve$1 () {
	if (hasRequiredResolve$1) return resolve$1;
	hasRequiredResolve$1 = 1;

	Object.defineProperty(resolve$1, "__esModule", {
	  value: true
	});
	resolve$1.resolveDocs = void 0;
	resolve$1.resolveDocs = {
	  name: 'resolve',
	  category: 'Algebra',
	  syntax: ['resolve(node, scope)'],
	  description: 'Recursively substitute variables in an expression tree.',
	  examples: ['resolve(parse("1 + x"), { x: 7 })', 'resolve(parse("size(text)"), { text: "Hello World" })', 'resolve(parse("x + y"), { x: parse("3z") })', 'resolve(parse("3x"), { x: parse("y+z"), z: parse("w^y") })'],
	  seealso: ['simplify', 'evaluate'],
	  mayThrow: ['ReferenceError']
	};
	return resolve$1;
}

var simplify$1 = {};

var hasRequiredSimplify$1;

function requireSimplify$1 () {
	if (hasRequiredSimplify$1) return simplify$1;
	hasRequiredSimplify$1 = 1;

	Object.defineProperty(simplify$1, "__esModule", {
	  value: true
	});
	simplify$1.simplifyDocs = void 0;
	simplify$1.simplifyDocs = {
	  name: 'simplify',
	  category: 'Algebra',
	  syntax: ['simplify(expr)', 'simplify(expr, rules)'],
	  description: 'Simplify an expression tree.',
	  examples: ['simplify("3 + 2 / 4")', 'simplify("2x + x")', 'f = parse("x * (x + 2 + x)")', 'simplified = simplify(f)', 'simplified.evaluate({x: 2})'],
	  seealso: ['simplifyCore', 'derivative', 'evaluate', 'parse', 'rationalize', 'resolve']
	};
	return simplify$1;
}

var simplifyConstant$1 = {};

var hasRequiredSimplifyConstant$1;

function requireSimplifyConstant$1 () {
	if (hasRequiredSimplifyConstant$1) return simplifyConstant$1;
	hasRequiredSimplifyConstant$1 = 1;

	Object.defineProperty(simplifyConstant$1, "__esModule", {
	  value: true
	});
	simplifyConstant$1.simplifyConstantDocs = void 0;
	simplifyConstant$1.simplifyConstantDocs = {
	  name: 'simplifyConstant',
	  category: 'Algebra',
	  syntax: ['simplifyConstant(expr)', 'simplifyConstant(expr, options)'],
	  description: 'Replace constant subexpressions of node with their values.',
	  examples: ['simplifyConstant("(3-3)*x")', 'simplifyConstant(parse("z-cos(tau/8)"))'],
	  seealso: ['simplify', 'simplifyCore', 'evaluate']
	};
	return simplifyConstant$1;
}

var simplifyCore$1 = {};

var hasRequiredSimplifyCore$1;

function requireSimplifyCore$1 () {
	if (hasRequiredSimplifyCore$1) return simplifyCore$1;
	hasRequiredSimplifyCore$1 = 1;

	Object.defineProperty(simplifyCore$1, "__esModule", {
	  value: true
	});
	simplifyCore$1.simplifyCoreDocs = void 0;
	simplifyCore$1.simplifyCoreDocs = {
	  name: 'simplifyCore',
	  category: 'Algebra',
	  syntax: ['simplifyCore(node)'],
	  description: 'Perform simple one-pass simplifications on an expression tree.',
	  examples: ['simplifyCore(parse("0*x"))', 'simplifyCore(parse("(x+0)*2"))'],
	  seealso: ['simplify', 'simplifyConstant', 'evaluate']
	};
	return simplifyCore$1;
}

var slu = {};

var hasRequiredSlu;

function requireSlu () {
	if (hasRequiredSlu) return slu;
	hasRequiredSlu = 1;

	Object.defineProperty(slu, "__esModule", {
	  value: true
	});
	slu.sluDocs = void 0;
	slu.sluDocs = {
	  name: 'slu',
	  category: 'Algebra',
	  syntax: ['slu(A, order, threshold)'],
	  description: 'Calculate the Matrix LU decomposition with full pivoting. Matrix A is decomposed in two matrices (L, U) and two permutation vectors (pinv, q) where P * A * Q = L * U',
	  examples: ['slu(sparse([4.5, 0, 3.2, 0; 3.1, 2.9, 0, 0.9; 0, 1.7, 3, 0; 3.5, 0.4, 0, 1]), 1, 0.001)'],
	  seealso: ['lusolve', 'lsolve', 'usolve', 'matrix', 'sparse', 'lup', 'qr']
	};
	return slu;
}

var symbolicEqual$1 = {};

var hasRequiredSymbolicEqual$1;

function requireSymbolicEqual$1 () {
	if (hasRequiredSymbolicEqual$1) return symbolicEqual$1;
	hasRequiredSymbolicEqual$1 = 1;

	Object.defineProperty(symbolicEqual$1, "__esModule", {
	  value: true
	});
	symbolicEqual$1.symbolicEqualDocs = void 0;
	symbolicEqual$1.symbolicEqualDocs = {
	  name: 'symbolicEqual',
	  category: 'Algebra',
	  syntax: ['symbolicEqual(expr1, expr2)', 'symbolicEqual(expr1, expr2, options)'],
	  description: 'Returns true if the difference of the expressions simplifies to 0',
	  examples: ['symbolicEqual("x*y","y*x")', 'symbolicEqual("abs(x^2)", "x^2")', 'symbolicEqual("abs(x)", "x", {context: {abs: {trivial: true}}})'],
	  seealso: ['simplify', 'evaluate']
	};
	return symbolicEqual$1;
}

var usolve = {};

var hasRequiredUsolve;

function requireUsolve () {
	if (hasRequiredUsolve) return usolve;
	hasRequiredUsolve = 1;

	Object.defineProperty(usolve, "__esModule", {
	  value: true
	});
	usolve.usolveDocs = void 0;
	usolve.usolveDocs = {
	  name: 'usolve',
	  category: 'Algebra',
	  syntax: ['x=usolve(U, b)'],
	  description: 'Finds one solution of the linear system U * x = b where U is an [n x n] upper triangular matrix and b is a [n] column vector.',
	  examples: ['x=usolve(sparse([1, 1, 1, 1; 0, 1, 1, 1; 0, 0, 1, 1; 0, 0, 0, 1]), [1; 2; 3; 4])'],
	  seealso: ['usolveAll', 'lup', 'lusolve', 'lsolve', 'matrix', 'sparse']
	};
	return usolve;
}

var usolveAll = {};

var hasRequiredUsolveAll;

function requireUsolveAll () {
	if (hasRequiredUsolveAll) return usolveAll;
	hasRequiredUsolveAll = 1;

	Object.defineProperty(usolveAll, "__esModule", {
	  value: true
	});
	usolveAll.usolveAllDocs = void 0;
	usolveAll.usolveAllDocs = {
	  name: 'usolveAll',
	  category: 'Algebra',
	  syntax: ['x=usolve(U, b)'],
	  description: 'Finds all solutions of the linear system U * x = b where U is an [n x n] upper triangular matrix and b is a [n] column vector.',
	  examples: ['x=usolve(sparse([1, 1, 1, 1; 0, 1, 1, 1; 0, 0, 1, 1; 0, 0, 0, 1]), [1; 2; 3; 4])'],
	  seealso: ['usolve', 'lup', 'lusolve', 'lsolve', 'matrix', 'sparse']
	};
	return usolveAll;
}

var abs = {};

var hasRequiredAbs;

function requireAbs () {
	if (hasRequiredAbs) return abs;
	hasRequiredAbs = 1;

	Object.defineProperty(abs, "__esModule", {
	  value: true
	});
	abs.absDocs = void 0;
	abs.absDocs = {
	  name: 'abs',
	  category: 'Arithmetic',
	  syntax: ['abs(x)'],
	  description: 'Compute the absolute value.',
	  examples: ['abs(3.5)', 'abs(-4.2)'],
	  seealso: ['sign']
	};
	return abs;
}

var add = {};

var hasRequiredAdd;

function requireAdd () {
	if (hasRequiredAdd) return add;
	hasRequiredAdd = 1;

	Object.defineProperty(add, "__esModule", {
	  value: true
	});
	add.addDocs = void 0;
	add.addDocs = {
	  name: 'add',
	  category: 'Operators',
	  syntax: ['x + y', 'add(x, y)'],
	  description: 'Add two values.',
	  examples: ['a = 2.1 + 3.6', 'a - 3.6', '3 + 2i', '3 cm + 2 inch', '"2.3" + "4"'],
	  seealso: ['subtract']
	};
	return add;
}

var cbrt = {};

var hasRequiredCbrt;

function requireCbrt () {
	if (hasRequiredCbrt) return cbrt;
	hasRequiredCbrt = 1;

	Object.defineProperty(cbrt, "__esModule", {
	  value: true
	});
	cbrt.cbrtDocs = void 0;
	cbrt.cbrtDocs = {
	  name: 'cbrt',
	  category: 'Arithmetic',
	  syntax: ['cbrt(x)', 'cbrt(x, allRoots)'],
	  description: 'Compute the cubic root value. If x = y * y * y, then y is the cubic root of x. When `x` is a number or complex number, an optional second argument `allRoots` can be provided to return all three cubic roots. If not provided, the principal root is returned',
	  examples: ['cbrt(64)', 'cube(4)', 'cbrt(-8)', 'cbrt(2 + 3i)', 'cbrt(8i)', 'cbrt(8i, true)', 'cbrt(27 m^3)'],
	  seealso: ['square', 'sqrt', 'cube', 'multiply']
	};
	return cbrt;
}

var ceil = {};

var hasRequiredCeil;

function requireCeil () {
	if (hasRequiredCeil) return ceil;
	hasRequiredCeil = 1;

	Object.defineProperty(ceil, "__esModule", {
	  value: true
	});
	ceil.ceilDocs = void 0;
	ceil.ceilDocs = {
	  name: 'ceil',
	  category: 'Arithmetic',
	  syntax: ['ceil(x)', 'ceil(x, n)', 'ceil(unit, valuelessUnit)', 'ceil(unit, n, valuelessUnit)'],
	  description: 'Round a value towards plus infinity. If x is complex, both real and imaginary part are rounded towards plus infinity.',
	  examples: ['ceil(3.2)', 'ceil(3.8)', 'ceil(-4.2)', 'ceil(3.241cm, cm)', 'ceil(3.241cm, 2, cm)'],
	  seealso: ['floor', 'fix', 'round']
	};
	return ceil;
}

var cube = {};

var hasRequiredCube;

function requireCube () {
	if (hasRequiredCube) return cube;
	hasRequiredCube = 1;

	Object.defineProperty(cube, "__esModule", {
	  value: true
	});
	cube.cubeDocs = void 0;
	cube.cubeDocs = {
	  name: 'cube',
	  category: 'Arithmetic',
	  syntax: ['cube(x)'],
	  description: 'Compute the cube of a value. The cube of x is x * x * x.',
	  examples: ['cube(2)', '2^3', '2 * 2 * 2'],
	  seealso: ['multiply', 'square', 'pow']
	};
	return cube;
}

var divide$1 = {};

var hasRequiredDivide$1;

function requireDivide$1 () {
	if (hasRequiredDivide$1) return divide$1;
	hasRequiredDivide$1 = 1;

	Object.defineProperty(divide$1, "__esModule", {
	  value: true
	});
	divide$1.divideDocs = void 0;
	divide$1.divideDocs = {
	  name: 'divide',
	  category: 'Operators',
	  syntax: ['x / y', 'divide(x, y)'],
	  description: 'Divide two values.',
	  examples: ['a = 2 / 3', 'a * 3', '4.5 / 2', '3 + 4 / 2', '(3 + 4) / 2', '18 km / 4.5'],
	  seealso: ['multiply']
	};
	return divide$1;
}

var dotDivide = {};

var hasRequiredDotDivide;

function requireDotDivide () {
	if (hasRequiredDotDivide) return dotDivide;
	hasRequiredDotDivide = 1;

	Object.defineProperty(dotDivide, "__esModule", {
	  value: true
	});
	dotDivide.dotDivideDocs = void 0;
	dotDivide.dotDivideDocs = {
	  name: 'dotDivide',
	  category: 'Operators',
	  syntax: ['x ./ y', 'dotDivide(x, y)'],
	  description: 'Divide two values element wise.',
	  examples: ['a = [1, 2, 3; 4, 5, 6]', 'b = [2, 1, 1; 3, 2, 5]', 'a ./ b'],
	  seealso: ['multiply', 'dotMultiply', 'divide']
	};
	return dotDivide;
}

var dotMultiply = {};

var hasRequiredDotMultiply;

function requireDotMultiply () {
	if (hasRequiredDotMultiply) return dotMultiply;
	hasRequiredDotMultiply = 1;

	Object.defineProperty(dotMultiply, "__esModule", {
	  value: true
	});
	dotMultiply.dotMultiplyDocs = void 0;
	dotMultiply.dotMultiplyDocs = {
	  name: 'dotMultiply',
	  category: 'Operators',
	  syntax: ['x .* y', 'dotMultiply(x, y)'],
	  description: 'Multiply two values element wise.',
	  examples: ['a = [1, 2, 3; 4, 5, 6]', 'b = [2, 1, 1; 3, 2, 5]', 'a .* b'],
	  seealso: ['multiply', 'divide', 'dotDivide']
	};
	return dotMultiply;
}

var dotPow = {};

var hasRequiredDotPow;

function requireDotPow () {
	if (hasRequiredDotPow) return dotPow;
	hasRequiredDotPow = 1;

	Object.defineProperty(dotPow, "__esModule", {
	  value: true
	});
	dotPow.dotPowDocs = void 0;
	dotPow.dotPowDocs = {
	  name: 'dotPow',
	  category: 'Operators',
	  syntax: ['x .^ y', 'dotPow(x, y)'],
	  description: 'Calculates the power of x to y element wise.',
	  examples: ['a = [1, 2, 3; 4, 5, 6]', 'a .^ 2'],
	  seealso: ['pow']
	};
	return dotPow;
}

var exp = {};

var hasRequiredExp;

function requireExp () {
	if (hasRequiredExp) return exp;
	hasRequiredExp = 1;

	Object.defineProperty(exp, "__esModule", {
	  value: true
	});
	exp.expDocs = void 0;
	exp.expDocs = {
	  name: 'exp',
	  category: 'Arithmetic',
	  syntax: ['exp(x)'],
	  description: 'Calculate the exponent of a value.',
	  examples: ['exp(1.3)', 'e ^ 1.3', 'log(exp(1.3))', 'x = 2.4', '(exp(i*x) == cos(x) + i*sin(x))   # Euler\'s formula'],
	  seealso: ['expm', 'expm1', 'pow', 'log']
	};
	return exp;
}

var expm$1 = {};

var hasRequiredExpm$1;

function requireExpm$1 () {
	if (hasRequiredExpm$1) return expm$1;
	hasRequiredExpm$1 = 1;

	Object.defineProperty(expm$1, "__esModule", {
	  value: true
	});
	expm$1.expmDocs = void 0;
	expm$1.expmDocs = {
	  name: 'expm',
	  category: 'Arithmetic',
	  syntax: ['exp(x)'],
	  description: 'Compute the matrix exponential, expm(A) = e^A. ' + 'The matrix must be square. ' + 'Not to be confused with exp(a), which performs element-wise exponentiation.',
	  examples: ['expm([[0,2],[0,0]])'],
	  seealso: ['exp']
	};
	return expm$1;
}

var expm1 = {};

var hasRequiredExpm1;

function requireExpm1 () {
	if (hasRequiredExpm1) return expm1;
	hasRequiredExpm1 = 1;

	Object.defineProperty(expm1, "__esModule", {
	  value: true
	});
	expm1.expm1Docs = void 0;
	expm1.expm1Docs = {
	  name: 'expm1',
	  category: 'Arithmetic',
	  syntax: ['expm1(x)'],
	  description: 'Calculate the value of subtracting 1 from the exponential value.',
	  examples: ['expm1(2)', 'pow(e, 2) - 1', 'log(expm1(2) + 1)'],
	  seealso: ['exp', 'pow', 'log']
	};
	return expm1;
}

var fix = {};

var hasRequiredFix;

function requireFix () {
	if (hasRequiredFix) return fix;
	hasRequiredFix = 1;

	Object.defineProperty(fix, "__esModule", {
	  value: true
	});
	fix.fixDocs = void 0;
	fix.fixDocs = {
	  name: 'fix',
	  category: 'Arithmetic',
	  syntax: ['fix(x)', 'fix(x, n)', 'fix(unit, valuelessUnit)', 'fix(unit, n, valuelessUnit)'],
	  description: 'Round a value towards zero. If x is complex, both real and imaginary part are rounded towards zero.',
	  examples: ['fix(3.2)', 'fix(3.8)', 'fix(-4.2)', 'fix(-4.8)', 'fix(3.241cm, cm)', 'fix(3.241cm, 2, cm)'],
	  seealso: ['ceil', 'floor', 'round']
	};
	return fix;
}

var floor = {};

var hasRequiredFloor;

function requireFloor () {
	if (hasRequiredFloor) return floor;
	hasRequiredFloor = 1;

	Object.defineProperty(floor, "__esModule", {
	  value: true
	});
	floor.floorDocs = void 0;
	floor.floorDocs = {
	  name: 'floor',
	  category: 'Arithmetic',
	  syntax: ['floor(x)', 'floor(x, n)', 'floor(unit, valuelessUnit)', 'floor(unit, n, valuelessUnit)'],
	  description: 'Round a value towards minus infinity.If x is complex, both real and imaginary part are rounded towards minus infinity.',
	  examples: ['floor(3.2)', 'floor(3.8)', 'floor(-4.2)', 'floor(3.241cm, cm)', 'floor(3.241cm, 2, cm)'],
	  seealso: ['ceil', 'fix', 'round']
	};
	return floor;
}

var gcd = {};

var hasRequiredGcd;

function requireGcd () {
	if (hasRequiredGcd) return gcd;
	hasRequiredGcd = 1;

	Object.defineProperty(gcd, "__esModule", {
	  value: true
	});
	gcd.gcdDocs = void 0;
	gcd.gcdDocs = {
	  name: 'gcd',
	  category: 'Arithmetic',
	  syntax: ['gcd(a, b)', 'gcd(a, b, c, ...)'],
	  description: 'Compute the greatest common divisor.',
	  examples: ['gcd(8, 12)', 'gcd(-4, 6)', 'gcd(25, 15, -10)'],
	  seealso: ['lcm', 'xgcd']
	};
	return gcd;
}

var hypot = {};

var hasRequiredHypot;

function requireHypot () {
	if (hasRequiredHypot) return hypot;
	hasRequiredHypot = 1;

	Object.defineProperty(hypot, "__esModule", {
	  value: true
	});
	hypot.hypotDocs = void 0;
	hypot.hypotDocs = {
	  name: 'hypot',
	  category: 'Arithmetic',
	  syntax: ['hypot(a, b, c, ...)', 'hypot([a, b, c, ...])'],
	  description: 'Calculate the hypotenuse of a list with values.',
	  examples: ['hypot(3, 4)', 'sqrt(3^2 + 4^2)', 'hypot(-2)', 'hypot([3, 4, 5])'],
	  seealso: ['abs', 'norm']
	};
	return hypot;
}

var invmod = {};

var hasRequiredInvmod;

function requireInvmod () {
	if (hasRequiredInvmod) return invmod;
	hasRequiredInvmod = 1;

	Object.defineProperty(invmod, "__esModule", {
	  value: true
	});
	invmod.invmodDocs = void 0;
	invmod.invmodDocs = {
	  name: 'invmod',
	  category: 'Arithmetic',
	  syntax: ['invmod(a, b)'],
	  description: 'Calculate the (modular) multiplicative inverse of a modulo b. Solution to the equation ax ≣ 1 (mod b)',
	  examples: ['invmod(8, 12)', 'invmod(7, 13)', 'invmod(15151, 15122)'],
	  seealso: ['gcd', 'xgcd']
	};
	return invmod;
}

var lcm = {};

var hasRequiredLcm;

function requireLcm () {
	if (hasRequiredLcm) return lcm;
	hasRequiredLcm = 1;

	Object.defineProperty(lcm, "__esModule", {
	  value: true
	});
	lcm.lcmDocs = void 0;
	lcm.lcmDocs = {
	  name: 'lcm',
	  category: 'Arithmetic',
	  syntax: ['lcm(x, y)'],
	  description: 'Compute the least common multiple.',
	  examples: ['lcm(4, 6)', 'lcm(6, 21)', 'lcm(6, 21, 5)'],
	  seealso: ['gcd']
	};
	return lcm;
}

var log = {};

var hasRequiredLog;

function requireLog () {
	if (hasRequiredLog) return log;
	hasRequiredLog = 1;

	Object.defineProperty(log, "__esModule", {
	  value: true
	});
	log.logDocs = void 0;
	log.logDocs = {
	  name: 'log',
	  category: 'Arithmetic',
	  syntax: ['log(x)', 'log(x, base)'],
	  description: 'Compute the logarithm of a value. If no base is provided, the natural logarithm of x is calculated. If base if provided, the logarithm is calculated for the specified base. log(x, base) is defined as log(x) / log(base).',
	  examples: ['log(3.5)', 'a = log(2.4)', 'exp(a)', '10 ^ 4', 'log(10000, 10)', 'log(10000) / log(10)', 'b = log(1024, 2)', '2 ^ b'],
	  seealso: ['exp', 'log1p', 'log2', 'log10']
	};
	return log;
}

var log10 = {};

var hasRequiredLog10;

function requireLog10 () {
	if (hasRequiredLog10) return log10;
	hasRequiredLog10 = 1;

	Object.defineProperty(log10, "__esModule", {
	  value: true
	});
	log10.log10Docs = void 0;
	log10.log10Docs = {
	  name: 'log10',
	  category: 'Arithmetic',
	  syntax: ['log10(x)'],
	  description: 'Compute the 10-base logarithm of a value.',
	  examples: ['log10(0.00001)', 'log10(10000)', '10 ^ 4', 'log(10000) / log(10)', 'log(10000, 10)'],
	  seealso: ['exp', 'log']
	};
	return log10;
}

var log1p = {};

var hasRequiredLog1p;

function requireLog1p () {
	if (hasRequiredLog1p) return log1p;
	hasRequiredLog1p = 1;

	Object.defineProperty(log1p, "__esModule", {
	  value: true
	});
	log1p.log1pDocs = void 0;
	log1p.log1pDocs = {
	  name: 'log1p',
	  category: 'Arithmetic',
	  syntax: ['log1p(x)', 'log1p(x, base)'],
	  description: 'Calculate the logarithm of a `value+1`',
	  examples: ['log1p(2.5)', 'exp(log1p(1.4))', 'pow(10, 4)', 'log1p(9999, 10)', 'log1p(9999) / log(10)'],
	  seealso: ['exp', 'log', 'log2', 'log10']
	};
	return log1p;
}

var log2 = {};

var hasRequiredLog2;

function requireLog2 () {
	if (hasRequiredLog2) return log2;
	hasRequiredLog2 = 1;

	Object.defineProperty(log2, "__esModule", {
	  value: true
	});
	log2.log2Docs = void 0;
	log2.log2Docs = {
	  name: 'log2',
	  category: 'Arithmetic',
	  syntax: ['log2(x)'],
	  description: 'Calculate the 2-base of a value. This is the same as calculating `log(x, 2)`.',
	  examples: ['log2(0.03125)', 'log2(16)', 'log2(16) / log2(2)', 'pow(2, 4)'],
	  seealso: ['exp', 'log1p', 'log', 'log10']
	};
	return log2;
}

var mod$1 = {};

var hasRequiredMod;

function requireMod () {
	if (hasRequiredMod) return mod$1;
	hasRequiredMod = 1;

	Object.defineProperty(mod$1, "__esModule", {
	  value: true
	});
	mod$1.modDocs = void 0;
	mod$1.modDocs = {
	  name: 'mod',
	  category: 'Operators',
	  syntax: ['x % y', 'x mod y', 'mod(x, y)'],
	  description: 'Calculates the modulus, the remainder of an integer division.',
	  examples: ['7 % 3', '11 % 2', '10 mod 4', 'isOdd(x) = x % 2', 'isOdd(2)', 'isOdd(3)'],
	  seealso: ['divide']
	};
	return mod$1;
}

var multiply = {};

var hasRequiredMultiply;

function requireMultiply () {
	if (hasRequiredMultiply) return multiply;
	hasRequiredMultiply = 1;

	Object.defineProperty(multiply, "__esModule", {
	  value: true
	});
	multiply.multiplyDocs = void 0;
	multiply.multiplyDocs = {
	  name: 'multiply',
	  category: 'Operators',
	  syntax: ['x * y', 'multiply(x, y)'],
	  description: 'multiply two values.',
	  examples: ['a = 2.1 * 3.4', 'a / 3.4', '2 * 3 + 4', '2 * (3 + 4)', '3 * 2.1 km'],
	  seealso: ['divide']
	};
	return multiply;
}

var norm = {};

var hasRequiredNorm;

function requireNorm () {
	if (hasRequiredNorm) return norm;
	hasRequiredNorm = 1;

	Object.defineProperty(norm, "__esModule", {
	  value: true
	});
	norm.normDocs = void 0;
	norm.normDocs = {
	  name: 'norm',
	  category: 'Arithmetic',
	  syntax: ['norm(x)', 'norm(x, p)'],
	  description: 'Calculate the norm of a number, vector or matrix.',
	  examples: ['abs(-3.5)', 'norm(-3.5)', 'norm(3 - 4i)', 'norm([1, 2, -3], Infinity)', 'norm([1, 2, -3], -Infinity)', 'norm([3, 4], 2)', 'norm([[1, 2], [3, 4]], 1)', 'norm([[1, 2], [3, 4]], "inf")', 'norm([[1, 2], [3, 4]], "fro")']
	};
	return norm;
}

var nthRoot = {};

var hasRequiredNthRoot;

function requireNthRoot () {
	if (hasRequiredNthRoot) return nthRoot;
	hasRequiredNthRoot = 1;

	Object.defineProperty(nthRoot, "__esModule", {
	  value: true
	});
	nthRoot.nthRootDocs = void 0;
	nthRoot.nthRootDocs = {
	  name: 'nthRoot',
	  category: 'Arithmetic',
	  syntax: ['nthRoot(a)', 'nthRoot(a, root)'],
	  description: 'Calculate the nth root of a value. ' + 'The principal nth root of a positive real number A, ' + 'is the positive real solution of the equation "x^root = A".',
	  examples: ['4 ^ 3', 'nthRoot(64, 3)', 'nthRoot(9, 2)', 'sqrt(9)'],
	  seealso: ['nthRoots', 'pow', 'sqrt']
	};
	return nthRoot;
}

var nthRoots = {};

var hasRequiredNthRoots;

function requireNthRoots () {
	if (hasRequiredNthRoots) return nthRoots;
	hasRequiredNthRoots = 1;

	Object.defineProperty(nthRoots, "__esModule", {
	  value: true
	});
	nthRoots.nthRootsDocs = void 0;
	nthRoots.nthRootsDocs = {
	  name: 'nthRoots',
	  category: 'Arithmetic',
	  syntax: ['nthRoots(A)', 'nthRoots(A, root)'],
	  description: '' + 'Calculate the nth roots of a value. ' + 'An nth root of a positive real number A, ' + 'is a positive real solution of the equation "x^root = A". ' + 'This function returns an array of complex values.',
	  examples: ['nthRoots(1)', 'nthRoots(1, 3)'],
	  seealso: ['sqrt', 'pow', 'nthRoot']
	};
	return nthRoots;
}

var pow = {};

var hasRequiredPow;

function requirePow () {
	if (hasRequiredPow) return pow;
	hasRequiredPow = 1;

	Object.defineProperty(pow, "__esModule", {
	  value: true
	});
	pow.powDocs = void 0;
	pow.powDocs = {
	  name: 'pow',
	  category: 'Operators',
	  syntax: ['x ^ y', 'pow(x, y)'],
	  description: 'Calculates the power of x to y, x^y.',
	  examples: ['2^3', '2*2*2', '1 + e ^ (pi * i)', 'pow([[1, 2], [4, 3]], 2)', 'pow([[1, 2], [4, 3]], -1)'],
	  seealso: ['multiply', 'nthRoot', 'nthRoots', 'sqrt']
	};
	return pow;
}

var round = {};

var hasRequiredRound;

function requireRound () {
	if (hasRequiredRound) return round;
	hasRequiredRound = 1;

	Object.defineProperty(round, "__esModule", {
	  value: true
	});
	round.roundDocs = void 0;
	round.roundDocs = {
	  name: 'round',
	  category: 'Arithmetic',
	  syntax: ['round(x)', 'round(x, n)', 'round(unit, valuelessUnit)', 'round(unit, n, valuelessUnit)'],
	  description: 'round a value towards the nearest integer.If x is complex, both real and imaginary part are rounded towards the nearest integer. When n is specified, the value is rounded to n decimals.',
	  examples: ['round(3.2)', 'round(3.8)', 'round(-4.2)', 'round(-4.8)', 'round(pi, 3)', 'round(123.45678, 2)', 'round(3.241cm, 2, cm)', 'round([3.2, 3.8, -4.7])'],
	  seealso: ['ceil', 'floor', 'fix']
	};
	return round;
}

var sign = {};

var hasRequiredSign;

function requireSign () {
	if (hasRequiredSign) return sign;
	hasRequiredSign = 1;

	Object.defineProperty(sign, "__esModule", {
	  value: true
	});
	sign.signDocs = void 0;
	sign.signDocs = {
	  name: 'sign',
	  category: 'Arithmetic',
	  syntax: ['sign(x)'],
	  description: 'Compute the sign of a value. The sign of a value x is 1 when x>0, -1 when x<0, and 0 when x=0.',
	  examples: ['sign(3.5)', 'sign(-4.2)', 'sign(0)'],
	  seealso: ['abs']
	};
	return sign;
}

var sqrt = {};

var hasRequiredSqrt;

function requireSqrt () {
	if (hasRequiredSqrt) return sqrt;
	hasRequiredSqrt = 1;

	Object.defineProperty(sqrt, "__esModule", {
	  value: true
	});
	sqrt.sqrtDocs = void 0;
	sqrt.sqrtDocs = {
	  name: 'sqrt',
	  category: 'Arithmetic',
	  syntax: ['sqrt(x)'],
	  description: 'Compute the square root value. If x = y * y, then y is the square root of x.',
	  examples: ['sqrt(25)', '5 * 5', 'sqrt(-1)'],
	  seealso: ['square', 'sqrtm', 'multiply', 'nthRoot', 'nthRoots', 'pow']
	};
	return sqrt;
}

var sqrtm$1 = {};

var hasRequiredSqrtm$1;

function requireSqrtm$1 () {
	if (hasRequiredSqrtm$1) return sqrtm$1;
	hasRequiredSqrtm$1 = 1;

	Object.defineProperty(sqrtm$1, "__esModule", {
	  value: true
	});
	sqrtm$1.sqrtmDocs = void 0;
	sqrtm$1.sqrtmDocs = {
	  name: 'sqrtm',
	  category: 'Arithmetic',
	  syntax: ['sqrtm(x)'],
	  description: 'Calculate the principal square root of a square matrix. The principal square root matrix `X` of another matrix `A` is such that `X * X = A`.',
	  examples: ['sqrtm([[33, 24], [48, 57]])'],
	  seealso: ['sqrt', 'abs', 'square', 'multiply']
	};
	return sqrtm$1;
}

var sylvester$1 = {};

var hasRequiredSylvester$1;

function requireSylvester$1 () {
	if (hasRequiredSylvester$1) return sylvester$1;
	hasRequiredSylvester$1 = 1;

	Object.defineProperty(sylvester$1, "__esModule", {
	  value: true
	});
	sylvester$1.sylvesterDocs = void 0;
	sylvester$1.sylvesterDocs = {
	  name: 'sylvester',
	  category: 'Algebra',
	  syntax: ['sylvester(A,B,C)'],
	  description: 'Solves the real-valued Sylvester equation AX+XB=C for X',
	  examples: ['sylvester([[-1, -2], [1, 1]], [[-2, 1], [-1, 2]], [[-3, 2], [3, 0]])', 'A = [[-1, -2], [1, 1]]; B = [[2, -1], [1, -2]]; C = [[-3, 2], [3, 0]]', 'sylvester(A, B, C)'],
	  seealso: ['schur', 'lyap']
	};
	return sylvester$1;
}

var schur$1 = {};

var hasRequiredSchur$1;

function requireSchur$1 () {
	if (hasRequiredSchur$1) return schur$1;
	hasRequiredSchur$1 = 1;

	Object.defineProperty(schur$1, "__esModule", {
	  value: true
	});
	schur$1.schurDocs = void 0;
	schur$1.schurDocs = {
	  name: 'schur',
	  category: 'Algebra',
	  syntax: ['schur(A)'],
	  description: 'Performs a real Schur decomposition of the real matrix A = UTU\'',
	  examples: ['schur([[1, 0], [-4, 3]])', 'A = [[1, 0], [-4, 3]]', 'schur(A)'],
	  seealso: ['lyap', 'sylvester']
	};
	return schur$1;
}

var lyap$1 = {};

var hasRequiredLyap$1;

function requireLyap$1 () {
	if (hasRequiredLyap$1) return lyap$1;
	hasRequiredLyap$1 = 1;

	Object.defineProperty(lyap$1, "__esModule", {
	  value: true
	});
	lyap$1.lyapDocs = void 0;
	lyap$1.lyapDocs = {
	  name: 'lyap',
	  category: 'Algebra',
	  syntax: ['lyap(A,Q)'],
	  description: 'Solves the Continuous-time Lyapunov equation AP+PA\'+Q=0 for P',
	  examples: ['lyap([[-2, 0], [1, -4]], [[3, 1], [1, 3]])', 'A = [[-2, 0], [1, -4]]', 'Q = [[3, 1], [1, 3]]', 'lyap(A,Q)'],
	  seealso: ['schur', 'sylvester']
	};
	return lyap$1;
}

var square = {};

var hasRequiredSquare;

function requireSquare () {
	if (hasRequiredSquare) return square;
	hasRequiredSquare = 1;

	Object.defineProperty(square, "__esModule", {
	  value: true
	});
	square.squareDocs = void 0;
	square.squareDocs = {
	  name: 'square',
	  category: 'Arithmetic',
	  syntax: ['square(x)'],
	  description: 'Compute the square of a value. The square of x is x * x.',
	  examples: ['square(3)', 'sqrt(9)', '3^2', '3 * 3'],
	  seealso: ['multiply', 'pow', 'sqrt', 'cube']
	};
	return square;
}

var subtract$1 = {};

var hasRequiredSubtract;

function requireSubtract () {
	if (hasRequiredSubtract) return subtract$1;
	hasRequiredSubtract = 1;

	Object.defineProperty(subtract$1, "__esModule", {
	  value: true
	});
	subtract$1.subtractDocs = void 0;
	subtract$1.subtractDocs = {
	  name: 'subtract',
	  category: 'Operators',
	  syntax: ['x - y', 'subtract(x, y)'],
	  description: 'subtract two values.',
	  examples: ['a = 5.3 - 2', 'a + 2', '2/3 - 1/6', '2 * 3 - 3', '2.1 km - 500m'],
	  seealso: ['add']
	};
	return subtract$1;
}

var unaryMinus = {};

var hasRequiredUnaryMinus;

function requireUnaryMinus () {
	if (hasRequiredUnaryMinus) return unaryMinus;
	hasRequiredUnaryMinus = 1;

	Object.defineProperty(unaryMinus, "__esModule", {
	  value: true
	});
	unaryMinus.unaryMinusDocs = void 0;
	unaryMinus.unaryMinusDocs = {
	  name: 'unaryMinus',
	  category: 'Operators',
	  syntax: ['-x', 'unaryMinus(x)'],
	  description: 'Inverse the sign of a value. Converts booleans and strings to numbers.',
	  examples: ['-4.5', '-(-5.6)', '-"22"'],
	  seealso: ['add', 'subtract', 'unaryPlus']
	};
	return unaryMinus;
}

var unaryPlus = {};

var hasRequiredUnaryPlus;

function requireUnaryPlus () {
	if (hasRequiredUnaryPlus) return unaryPlus;
	hasRequiredUnaryPlus = 1;

	Object.defineProperty(unaryPlus, "__esModule", {
	  value: true
	});
	unaryPlus.unaryPlusDocs = void 0;
	unaryPlus.unaryPlusDocs = {
	  name: 'unaryPlus',
	  category: 'Operators',
	  syntax: ['+x', 'unaryPlus(x)'],
	  description: 'Converts booleans and strings to numbers.',
	  examples: ['+true', '+"2"'],
	  seealso: ['add', 'subtract', 'unaryMinus']
	};
	return unaryPlus;
}

var xgcd = {};

var hasRequiredXgcd;

function requireXgcd () {
	if (hasRequiredXgcd) return xgcd;
	hasRequiredXgcd = 1;

	Object.defineProperty(xgcd, "__esModule", {
	  value: true
	});
	xgcd.xgcdDocs = void 0;
	xgcd.xgcdDocs = {
	  name: 'xgcd',
	  category: 'Arithmetic',
	  syntax: ['xgcd(a, b)'],
	  description: 'Calculate the extended greatest common divisor for two values. The result is an array [d, x, y] with 3 entries, where d is the greatest common divisor, and d = x * a + y * b.',
	  examples: ['xgcd(8, 12)', 'gcd(8, 12)', 'xgcd(36163, 21199)'],
	  seealso: ['gcd', 'lcm']
	};
	return xgcd;
}

var num$1 = {};

var hasRequiredNum$1;

function requireNum$1 () {
	if (hasRequiredNum$1) return num$1;
	hasRequiredNum$1 = 1;

	Object.defineProperty(num$1, "__esModule", {
	  value: true
	});
	num$1.numDocs = void 0;
	num$1.numDocs = {
	  name: 'num',
	  category: 'Fraction',
	  syntax: ['num(x)'],
	  description: 'Get the numerator of a fraction.',
	  examples: ['num(fraction(2, 3))', 'num(fraction(5, 8))'],
	  seealso: ['den', 'fraction']
	};
	return num$1;
}

var den$1 = {};

var hasRequiredDen$1;

function requireDen$1 () {
	if (hasRequiredDen$1) return den$1;
	hasRequiredDen$1 = 1;

	Object.defineProperty(den$1, "__esModule", {
	  value: true
	});
	den$1.denDocs = void 0;
	den$1.denDocs = {
	  name: 'den',
	  category: 'Fraction',
	  syntax: ['den(x)'],
	  description: 'Get the denominator of a fraction.',
	  examples: ['den(fraction(2, 3))', 'den(fraction(5, 8))'],
	  seealso: ['num', 'fraction']
	};
	return den$1;
}

var bitAnd = {};

var hasRequiredBitAnd;

function requireBitAnd () {
	if (hasRequiredBitAnd) return bitAnd;
	hasRequiredBitAnd = 1;

	Object.defineProperty(bitAnd, "__esModule", {
	  value: true
	});
	bitAnd.bitAndDocs = void 0;
	bitAnd.bitAndDocs = {
	  name: 'bitAnd',
	  category: 'Bitwise',
	  syntax: ['x & y', 'bitAnd(x, y)'],
	  description: 'Bitwise AND operation. Performs the logical AND operation on each pair of the corresponding bits of the two given values by multiplying them. If both bits in the compared position are 1, the bit in the resulting binary representation is 1, otherwise, the result is 0',
	  examples: ['5 & 3', 'bitAnd(53, 131)', '[1, 12, 31] & 42'],
	  seealso: ['bitNot', 'bitOr', 'bitXor', 'leftShift', 'rightArithShift', 'rightLogShift']
	};
	return bitAnd;
}

var bitNot = {};

var hasRequiredBitNot;

function requireBitNot () {
	if (hasRequiredBitNot) return bitNot;
	hasRequiredBitNot = 1;

	Object.defineProperty(bitNot, "__esModule", {
	  value: true
	});
	bitNot.bitNotDocs = void 0;
	bitNot.bitNotDocs = {
	  name: 'bitNot',
	  category: 'Bitwise',
	  syntax: ['~x', 'bitNot(x)'],
	  description: 'Bitwise NOT operation. Performs a logical negation on each bit of the given value. Bits that are 0 become 1, and those that are 1 become 0.',
	  examples: ['~1', '~2', 'bitNot([2, -3, 4])'],
	  seealso: ['bitAnd', 'bitOr', 'bitXor', 'leftShift', 'rightArithShift', 'rightLogShift']
	};
	return bitNot;
}

var bitOr = {};

var hasRequiredBitOr;

function requireBitOr () {
	if (hasRequiredBitOr) return bitOr;
	hasRequiredBitOr = 1;

	Object.defineProperty(bitOr, "__esModule", {
	  value: true
	});
	bitOr.bitOrDocs = void 0;
	bitOr.bitOrDocs = {
	  name: 'bitOr',
	  category: 'Bitwise',
	  syntax: ['x | y', 'bitOr(x, y)'],
	  description: 'Bitwise OR operation. Performs the logical inclusive OR operation on each pair of corresponding bits of the two given values. The result in each position is 1 if the first bit is 1 or the second bit is 1 or both bits are 1, otherwise, the result is 0.',
	  examples: ['5 | 3', 'bitOr([1, 2, 3], 4)'],
	  seealso: ['bitAnd', 'bitNot', 'bitXor', 'leftShift', 'rightArithShift', 'rightLogShift']
	};
	return bitOr;
}

var bitXor = {};

var hasRequiredBitXor;

function requireBitXor () {
	if (hasRequiredBitXor) return bitXor;
	hasRequiredBitXor = 1;

	Object.defineProperty(bitXor, "__esModule", {
	  value: true
	});
	bitXor.bitXorDocs = void 0;
	bitXor.bitXorDocs = {
	  name: 'bitXor',
	  category: 'Bitwise',
	  syntax: ['bitXor(x, y)'],
	  description: 'Bitwise XOR operation, exclusive OR. Performs the logical exclusive OR operation on each pair of corresponding bits of the two given values. The result in each position is 1 if only the first bit is 1 or only the second bit is 1, but will be 0 if both are 0 or both are 1.',
	  examples: ['bitOr(1, 2)', 'bitXor([2, 3, 4], 4)'],
	  seealso: ['bitAnd', 'bitNot', 'bitOr', 'leftShift', 'rightArithShift', 'rightLogShift']
	};
	return bitXor;
}

var leftShift = {};

var hasRequiredLeftShift;

function requireLeftShift () {
	if (hasRequiredLeftShift) return leftShift;
	hasRequiredLeftShift = 1;

	Object.defineProperty(leftShift, "__esModule", {
	  value: true
	});
	leftShift.leftShiftDocs = void 0;
	leftShift.leftShiftDocs = {
	  name: 'leftShift',
	  category: 'Bitwise',
	  syntax: ['x << y', 'leftShift(x, y)'],
	  description: 'Bitwise left logical shift of a value x by y number of bits.',
	  examples: ['4 << 1', '8 >> 1'],
	  seealso: ['bitAnd', 'bitNot', 'bitOr', 'bitXor', 'rightArithShift', 'rightLogShift']
	};
	return leftShift;
}

var rightArithShift = {};

var hasRequiredRightArithShift;

function requireRightArithShift () {
	if (hasRequiredRightArithShift) return rightArithShift;
	hasRequiredRightArithShift = 1;

	Object.defineProperty(rightArithShift, "__esModule", {
	  value: true
	});
	rightArithShift.rightArithShiftDocs = void 0;
	rightArithShift.rightArithShiftDocs = {
	  name: 'rightArithShift',
	  category: 'Bitwise',
	  syntax: ['x >> y', 'rightArithShift(x, y)'],
	  description: 'Bitwise right arithmetic shift of a value x by y number of bits.',
	  examples: ['8 >> 1', '4 << 1', '-12 >> 2'],
	  seealso: ['bitAnd', 'bitNot', 'bitOr', 'bitXor', 'leftShift', 'rightLogShift']
	};
	return rightArithShift;
}

var rightLogShift = {};

var hasRequiredRightLogShift;

function requireRightLogShift () {
	if (hasRequiredRightLogShift) return rightLogShift;
	hasRequiredRightLogShift = 1;

	Object.defineProperty(rightLogShift, "__esModule", {
	  value: true
	});
	rightLogShift.rightLogShiftDocs = void 0;
	rightLogShift.rightLogShiftDocs = {
	  name: 'rightLogShift',
	  category: 'Bitwise',
	  syntax: ['x >>> y', 'rightLogShift(x, y)'],
	  description: 'Bitwise right logical shift of a value x by y number of bits.',
	  examples: ['8 >>> 1', '4 << 1', '-12 >>> 2'],
	  seealso: ['bitAnd', 'bitNot', 'bitOr', 'bitXor', 'leftShift', 'rightArithShift']
	};
	return rightLogShift;
}

var bellNumbers$1 = {};

var hasRequiredBellNumbers$1;

function requireBellNumbers$1 () {
	if (hasRequiredBellNumbers$1) return bellNumbers$1;
	hasRequiredBellNumbers$1 = 1;

	Object.defineProperty(bellNumbers$1, "__esModule", {
	  value: true
	});
	bellNumbers$1.bellNumbersDocs = void 0;
	bellNumbers$1.bellNumbersDocs = {
	  name: 'bellNumbers',
	  category: 'Combinatorics',
	  syntax: ['bellNumbers(n)'],
	  description: 'The Bell Numbers count the number of partitions of a set. A partition is a pairwise disjoint subset of S whose union is S. `bellNumbers` only takes integer arguments. The following condition must be enforced: n >= 0.',
	  examples: ['bellNumbers(3)', 'bellNumbers(8)'],
	  seealso: ['stirlingS2']
	};
	return bellNumbers$1;
}

var catalan$1 = {};

var hasRequiredCatalan$1;

function requireCatalan$1 () {
	if (hasRequiredCatalan$1) return catalan$1;
	hasRequiredCatalan$1 = 1;

	Object.defineProperty(catalan$1, "__esModule", {
	  value: true
	});
	catalan$1.catalanDocs = void 0;
	catalan$1.catalanDocs = {
	  name: 'catalan',
	  category: 'Combinatorics',
	  syntax: ['catalan(n)'],
	  description: 'The Catalan Numbers enumerate combinatorial structures of many different types. catalan only takes integer arguments. The following condition must be enforced: n >= 0.',
	  examples: ['catalan(3)', 'catalan(8)'],
	  seealso: ['bellNumbers']
	};
	return catalan$1;
}

var composition$1 = {};

var hasRequiredComposition$1;

function requireComposition$1 () {
	if (hasRequiredComposition$1) return composition$1;
	hasRequiredComposition$1 = 1;

	Object.defineProperty(composition$1, "__esModule", {
	  value: true
	});
	composition$1.compositionDocs = void 0;
	composition$1.compositionDocs = {
	  name: 'composition',
	  category: 'Combinatorics',
	  syntax: ['composition(n, k)'],
	  description: 'The composition counts of n into k parts. composition only takes integer arguments. The following condition must be enforced: k <= n.',
	  examples: ['composition(5, 3)'],
	  seealso: ['combinations']
	};
	return composition$1;
}

var stirlingS2$1 = {};

var hasRequiredStirlingS2$1;

function requireStirlingS2$1 () {
	if (hasRequiredStirlingS2$1) return stirlingS2$1;
	hasRequiredStirlingS2$1 = 1;

	Object.defineProperty(stirlingS2$1, "__esModule", {
	  value: true
	});
	stirlingS2$1.stirlingS2Docs = void 0;
	stirlingS2$1.stirlingS2Docs = {
	  name: 'stirlingS2',
	  category: 'Combinatorics',
	  syntax: ['stirlingS2(n, k)'],
	  description: 'The Stirling numbers of the second kind, counts the number of ways to partition a set of n labelled objects into k nonempty unlabelled subsets. `stirlingS2` only takes integer arguments. The following condition must be enforced: k <= n. If n = k or k = 1, then s(n,k) = 1.',
	  examples: ['stirlingS2(5, 3)'],
	  seealso: ['bellNumbers', 'bernoulli']
	};
	return stirlingS2$1;
}

var arg = {};

var hasRequiredArg;

function requireArg () {
	if (hasRequiredArg) return arg;
	hasRequiredArg = 1;

	Object.defineProperty(arg, "__esModule", {
	  value: true
	});
	arg.argDocs = void 0;
	arg.argDocs = {
	  name: 'arg',
	  category: 'Complex',
	  syntax: ['arg(x)'],
	  description: 'Compute the argument of a complex value. If x = a+bi, the argument is computed as atan2(b, a).',
	  examples: ['arg(2 + 2i)', 'atan2(3, 2)', 'arg(2 + 3i)'],
	  seealso: ['re', 'im', 'conj', 'abs']
	};
	return arg;
}

var conj = {};

var hasRequiredConj;

function requireConj () {
	if (hasRequiredConj) return conj;
	hasRequiredConj = 1;

	Object.defineProperty(conj, "__esModule", {
	  value: true
	});
	conj.conjDocs = void 0;
	conj.conjDocs = {
	  name: 'conj',
	  category: 'Complex',
	  syntax: ['conj(x)'],
	  description: 'Compute the complex conjugate of a complex value. If x = a+bi, the complex conjugate is a-bi.',
	  examples: ['conj(2 + 3i)', 'conj(2 - 3i)', 'conj(-5.2i)'],
	  seealso: ['re', 'im', 'abs', 'arg']
	};
	return conj;
}

var im = {};

var hasRequiredIm;

function requireIm () {
	if (hasRequiredIm) return im;
	hasRequiredIm = 1;

	Object.defineProperty(im, "__esModule", {
	  value: true
	});
	im.imDocs = void 0;
	im.imDocs = {
	  name: 'im',
	  category: 'Complex',
	  syntax: ['im(x)'],
	  description: 'Get the imaginary part of a complex number.',
	  examples: ['im(2 + 3i)', 're(2 + 3i)', 'im(-5.2i)', 'im(2.4)'],
	  seealso: ['re', 'conj', 'abs', 'arg']
	};
	return im;
}

var re = {};

var hasRequiredRe;

function requireRe () {
	if (hasRequiredRe) return re;
	hasRequiredRe = 1;

	Object.defineProperty(re, "__esModule", {
	  value: true
	});
	re.reDocs = void 0;
	re.reDocs = {
	  name: 're',
	  category: 'Complex',
	  syntax: ['re(x)'],
	  description: 'Get the real part of a complex number.',
	  examples: ['re(2 + 3i)', 'im(2 + 3i)', 're(-5.2i)', 're(2.4)'],
	  seealso: ['im', 'conj', 'abs', 'arg']
	};
	return re;
}

var evaluate = {};

var hasRequiredEvaluate;

function requireEvaluate () {
	if (hasRequiredEvaluate) return evaluate;
	hasRequiredEvaluate = 1;

	Object.defineProperty(evaluate, "__esModule", {
	  value: true
	});
	evaluate.evaluateDocs = void 0;
	evaluate.evaluateDocs = {
	  name: 'evaluate',
	  category: 'Expression',
	  syntax: ['evaluate(expression)', 'evaluate(expression, scope)', 'evaluate([expr1, expr2, expr3, ...])', 'evaluate([expr1, expr2, expr3, ...], scope)'],
	  description: 'Evaluate an expression or an array with expressions.',
	  examples: ['evaluate("2 + 3")', 'evaluate("sqrt(16)")', 'evaluate("2 inch to cm")', 'evaluate("sin(x * pi)", { "x": 1/2 })', 'evaluate(["width=2", "height=4","width*height"])'],
	  seealso: ['parser', 'parse', 'compile']
	};
	return evaluate;
}

var parser$1 = {};

var hasRequiredParser$1;

function requireParser$1 () {
	if (hasRequiredParser$1) return parser$1;
	hasRequiredParser$1 = 1;

	Object.defineProperty(parser$1, "__esModule", {
	  value: true
	});
	parser$1.parserDocs = void 0;
	parser$1.parserDocs = {
	  name: 'parser',
	  category: 'Expression',
	  syntax: ['parser()'],
	  description: 'Create a parser object that keeps a context of variables and their values, allowing the evaluation of expressions in that context.',
	  examples: ['myParser = parser()', 'myParser.evaluate("sqrt(3^2 + 4^2)")', 'myParser.set("x", 3)', 'myParser.evaluate("y = x + 3")', 'myParser.evaluate(["y = x + 3", "y = y + 1"])', 'myParser.get("y")'],
	  seealso: ['evaluate', 'parse', 'compile']
	};
	return parser$1;
}

var parse = {};

var hasRequiredParse$1;

function requireParse$1 () {
	if (hasRequiredParse$1) return parse;
	hasRequiredParse$1 = 1;

	Object.defineProperty(parse, "__esModule", {
	  value: true
	});
	parse.parseDocs = void 0;
	parse.parseDocs = {
	  name: 'parse',
	  category: 'Expression',
	  syntax: ['parse(expr)', 'parse(expr, options)', 'parse([expr1, expr2, expr3, ...])', 'parse([expr1, expr2, expr3, ...], options)'],
	  description: 'Parse an expression. Returns a node tree, which can be evaluated by invoking node.evaluate() or transformed into a functional object via node.compile().',
	  examples: ['node1 = parse("sqrt(3^2 + 4^2)")', 'node1.evaluate()', 'code1 = node1.compile()', 'code1.evaluate()', 'scope = {a: 3, b: 4}', 'node2 = parse("a * b")', 'node2.evaluate(scope)', 'code2 = node2.compile()', 'code2.evaluate(scope)'],
	  seealso: ['parser', 'evaluate', 'compile']
	};
	return parse;
}

var compile$1 = {};

var hasRequiredCompile$1;

function requireCompile$1 () {
	if (hasRequiredCompile$1) return compile$1;
	hasRequiredCompile$1 = 1;

	Object.defineProperty(compile$1, "__esModule", {
	  value: true
	});
	compile$1.compileDocs = void 0;
	compile$1.compileDocs = {
	  name: 'compile',
	  category: 'Expression',
	  syntax: ['compile(expr) ', 'compile([expr1, expr2, expr3, ...])'],
	  description: 'Parse and compile an expression. Returns a an object with a function evaluate([scope]) to evaluate the compiled expression.',
	  examples: ['code1 = compile("sqrt(3^2 + 4^2)")', 'code1.evaluate() ', 'code2 = compile("a * b")', 'code2.evaluate({a: 3, b: 4})'],
	  seealso: ['parser', 'parse', 'evaluate']
	};
	return compile$1;
}

var help = {};

var hasRequiredHelp$1;

function requireHelp$1 () {
	if (hasRequiredHelp$1) return help;
	hasRequiredHelp$1 = 1;

	Object.defineProperty(help, "__esModule", {
	  value: true
	});
	help.helpDocs = void 0;
	help.helpDocs = {
	  name: 'help',
	  category: 'Expression',
	  syntax: ['help(object)', 'help(string)'],
	  description: 'Display documentation on a function or data type.',
	  examples: ['help(sqrt)', 'help("complex")'],
	  seealso: []
	};
	return help;
}

var distance$2 = {};

var hasRequiredDistance$1;

function requireDistance$1 () {
	if (hasRequiredDistance$1) return distance$2;
	hasRequiredDistance$1 = 1;

	Object.defineProperty(distance$2, "__esModule", {
	  value: true
	});
	distance$2.distanceDocs = void 0;
	distance$2.distanceDocs = {
	  name: 'distance',
	  category: 'Geometry',
	  syntax: ['distance([x1, y1], [x2, y2])', 'distance([[x1, y1], [x2, y2]])'],
	  description: 'Calculates the Euclidean distance between two points.',
	  examples: ['distance([0,0], [4,4])', 'distance([[0,0], [4,4]])'],
	  seealso: []
	};
	return distance$2;
}

var intersect$3 = {};

var hasRequiredIntersect$1;

function requireIntersect$1 () {
	if (hasRequiredIntersect$1) return intersect$3;
	hasRequiredIntersect$1 = 1;

	Object.defineProperty(intersect$3, "__esModule", {
	  value: true
	});
	intersect$3.intersectDocs = void 0;
	intersect$3.intersectDocs = {
	  name: 'intersect',
	  category: 'Geometry',
	  syntax: ['intersect(expr1, expr2, expr3, expr4)', 'intersect(expr1, expr2, expr3)'],
	  description: 'Computes the intersection point of lines and/or planes.',
	  examples: ['intersect([0, 0], [10, 10], [10, 0], [0, 10])', 'intersect([1, 0, 1],  [4, -2, 2], [1, 1, 1, 6])'],
	  seealso: []
	};
	return intersect$3;
}

var and = {};

var hasRequiredAnd;

function requireAnd () {
	if (hasRequiredAnd) return and;
	hasRequiredAnd = 1;

	Object.defineProperty(and, "__esModule", {
	  value: true
	});
	and.andDocs = void 0;
	and.andDocs = {
	  name: 'and',
	  category: 'Logical',
	  syntax: ['x and y', 'and(x, y)'],
	  description: 'Logical and. Test whether two values are both defined with a nonzero/nonempty value.',
	  examples: ['true and false', 'true and true', '2 and 4'],
	  seealso: ['not', 'or', 'xor']
	};
	return and;
}

var not = {};

var hasRequiredNot;

function requireNot () {
	if (hasRequiredNot) return not;
	hasRequiredNot = 1;

	Object.defineProperty(not, "__esModule", {
	  value: true
	});
	not.notDocs = void 0;
	not.notDocs = {
	  name: 'not',
	  category: 'Logical',
	  syntax: ['not x', 'not(x)'],
	  description: 'Logical not. Flips the boolean value of given argument.',
	  examples: ['not true', 'not false', 'not 2', 'not 0'],
	  seealso: ['and', 'or', 'xor']
	};
	return not;
}

var nullish = {};

var hasRequiredNullish;

function requireNullish () {
	if (hasRequiredNullish) return nullish;
	hasRequiredNullish = 1;

	Object.defineProperty(nullish, "__esModule", {
	  value: true
	});
	nullish.nullishDocs = void 0;
	nullish.nullishDocs = {
	  name: 'nullish',
	  category: 'Logical',
	  syntax: ['x ?? y', 'nullish(x, y)'],
	  description: 'Nullish coalescing operator. Returns the right-hand operand when the left-hand operand is null or undefined, and otherwise returns the left-hand operand.',
	  examples: ['null ?? 42', 'undefined ?? 42', '0 ?? 42', 'false ?? 42', 'null ?? undefined ?? 42'],
	  seealso: ['and', 'or', 'not']
	};
	return nullish;
}

var or = {};

var hasRequiredOr;

function requireOr () {
	if (hasRequiredOr) return or;
	hasRequiredOr = 1;

	Object.defineProperty(or, "__esModule", {
	  value: true
	});
	or.orDocs = void 0;
	or.orDocs = {
	  name: 'or',
	  category: 'Logical',
	  syntax: ['x or y', 'or(x, y)'],
	  description: 'Logical or. Test if at least one value is defined with a nonzero/nonempty value.',
	  examples: ['true or false', 'false or false', '0 or 4'],
	  seealso: ['not', 'and', 'xor']
	};
	return or;
}

var xor = {};

var hasRequiredXor;

function requireXor () {
	if (hasRequiredXor) return xor;
	hasRequiredXor = 1;

	Object.defineProperty(xor, "__esModule", {
	  value: true
	});
	xor.xorDocs = void 0;
	xor.xorDocs = {
	  name: 'xor',
	  category: 'Logical',
	  syntax: ['x xor y', 'xor(x, y)'],
	  description: 'Logical exclusive or, xor. Test whether one and only one value is defined with a nonzero/nonempty value.',
	  examples: ['true xor false', 'false xor false', 'true xor true', '0 xor 4'],
	  seealso: ['not', 'and', 'or']
	};
	return xor;
}

var mapSlices = {};

var hasRequiredMapSlices;

function requireMapSlices () {
	if (hasRequiredMapSlices) return mapSlices;
	hasRequiredMapSlices = 1;

	Object.defineProperty(mapSlices, "__esModule", {
	  value: true
	});
	mapSlices.mapSlicesDocs = void 0;
	mapSlices.mapSlicesDocs = {
	  name: 'mapSlices',
	  category: 'Matrix',
	  syntax: ['mapSlices(A, dim, callback)'],
	  description: 'Generate a matrix one dimension less than A by applying callback to ' + 'each slice of A along dimension dim.',
	  examples: ['A = [[1, 2], [3, 4]]', 'mapSlices(A, 1, sum)',
	  // returns [4, 6]
	  'mapSlices(A, 2, prod)' // returns [2, 12]
	  ],
	  seealso: ['map', 'forEach']
	};
	return mapSlices;
}

var column = {};

var hasRequiredColumn;

function requireColumn () {
	if (hasRequiredColumn) return column;
	hasRequiredColumn = 1;

	Object.defineProperty(column, "__esModule", {
	  value: true
	});
	column.columnDocs = void 0;
	column.columnDocs = {
	  name: 'column',
	  category: 'Matrix',
	  syntax: ['column(x, index)'],
	  description: 'Return a column from a matrix or array.',
	  examples: ['A = [[1, 2], [3, 4]]', 'column(A, 1)', 'column(A, 2)'],
	  seealso: ['row', 'matrixFromColumns']
	};
	return column;
}

var concat = {};

var hasRequiredConcat;

function requireConcat () {
	if (hasRequiredConcat) return concat;
	hasRequiredConcat = 1;

	Object.defineProperty(concat, "__esModule", {
	  value: true
	});
	concat.concatDocs = void 0;
	concat.concatDocs = {
	  name: 'concat',
	  category: 'Matrix',
	  syntax: ['concat(A, B, C, ...)', 'concat(A, B, C, ..., dim)'],
	  description: 'Concatenate matrices. By default, the matrices are concatenated by the last dimension. The dimension on which to concatenate can be provided as last argument.',
	  examples: ['A = [1, 2; 5, 6]', 'B = [3, 4; 7, 8]', 'concat(A, B)', 'concat(A, B, 1)', 'concat(A, B, 2)'],
	  seealso: ['det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return concat;
}

var count = {};

var hasRequiredCount;

function requireCount () {
	if (hasRequiredCount) return count;
	hasRequiredCount = 1;

	Object.defineProperty(count, "__esModule", {
	  value: true
	});
	count.countDocs = void 0;
	count.countDocs = {
	  name: 'count',
	  category: 'Matrix',
	  syntax: ['count(x)'],
	  description: 'Count the number of elements of a matrix, array or string.',
	  examples: ['a = [1, 2; 3, 4; 5, 6]', 'count(a)', 'size(a)', 'count("hello world")'],
	  seealso: ['size']
	};
	return count;
}

var cross = {};

var hasRequiredCross;

function requireCross () {
	if (hasRequiredCross) return cross;
	hasRequiredCross = 1;

	Object.defineProperty(cross, "__esModule", {
	  value: true
	});
	cross.crossDocs = void 0;
	cross.crossDocs = {
	  name: 'cross',
	  category: 'Matrix',
	  syntax: ['cross(A, B)'],
	  description: 'Calculate the cross product for two vectors in three dimensional space.',
	  examples: ['cross([1, 1, 0],  [0, 1, 1])', 'cross([3, -3, 1], [4, 9, 2])', 'cross([2, 3, 4],  [5, 6, 7])'],
	  seealso: ['multiply', 'dot']
	};
	return cross;
}

var ctranspose = {};

var hasRequiredCtranspose;

function requireCtranspose () {
	if (hasRequiredCtranspose) return ctranspose;
	hasRequiredCtranspose = 1;

	Object.defineProperty(ctranspose, "__esModule", {
	  value: true
	});
	ctranspose.ctransposeDocs = void 0;
	ctranspose.ctransposeDocs = {
	  name: 'ctranspose',
	  category: 'Matrix',
	  syntax: ['x\'', 'ctranspose(x)'],
	  description: 'Complex Conjugate and Transpose a matrix',
	  examples: ['a = [1, 2, 3; 4, 5, 6]', 'a\'', 'ctranspose(a)'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'zeros']
	};
	return ctranspose;
}

var det$1 = {};

var hasRequiredDet$1;

function requireDet$1 () {
	if (hasRequiredDet$1) return det$1;
	hasRequiredDet$1 = 1;

	Object.defineProperty(det$1, "__esModule", {
	  value: true
	});
	det$1.detDocs = void 0;
	det$1.detDocs = {
	  name: 'det',
	  category: 'Matrix',
	  syntax: ['det(x)'],
	  description: 'Calculate the determinant of a matrix',
	  examples: ['det([1, 2; 3, 4])', 'det([-2, 2, 3; -1, 1, 3; 2, 0, -1])'],
	  seealso: ['concat', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return det$1;
}

var diag = {};

var hasRequiredDiag;

function requireDiag () {
	if (hasRequiredDiag) return diag;
	hasRequiredDiag = 1;

	Object.defineProperty(diag, "__esModule", {
	  value: true
	});
	diag.diagDocs = void 0;
	diag.diagDocs = {
	  name: 'diag',
	  category: 'Matrix',
	  syntax: ['diag(x)', 'diag(x, k)'],
	  description: 'Create a diagonal matrix or retrieve the diagonal of a matrix. When x is a vector, a matrix with the vector values on the diagonal will be returned. When x is a matrix, a vector with the diagonal values of the matrix is returned. When k is provided, the k-th diagonal will be filled in or retrieved, if k is positive, the values are placed on the super diagonal. When k is negative, the values are placed on the sub diagonal.',
	  examples: ['diag(1:3)', 'diag(1:3, 1)', 'a = [1, 2, 3; 4, 5, 6; 7, 8, 9]', 'diag(a)'],
	  seealso: ['concat', 'det', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return diag;
}

var diff = {};

var hasRequiredDiff;

function requireDiff () {
	if (hasRequiredDiff) return diff;
	hasRequiredDiff = 1;

	Object.defineProperty(diff, "__esModule", {
	  value: true
	});
	diff.diffDocs = void 0;
	diff.diffDocs = {
	  name: 'diff',
	  category: 'Matrix',
	  syntax: ['diff(arr)', 'diff(arr, dim)'],
	  description: ['Create a new matrix or array with the difference of the passed matrix or array.', 'Dim parameter is optional and used to indicate the dimension of the array/matrix to apply the difference', 'If no dimension parameter is passed it is assumed as dimension 0', 'Dimension is zero-based in javascript and one-based in the parser', 'Arrays must be \'rectangular\' meaning arrays like [1, 2]', 'If something is passed as a matrix it will be returned as a matrix but other than that all matrices are converted to arrays'],
	  examples: ['A = [1, 2, 4, 7, 0]', 'diff(A)', 'diff(A, 1)', 'B = [[1, 2], [3, 4]]', 'diff(B)', 'diff(B, 1)', 'diff(B, 2)', 'diff(B, bignumber(2))', 'diff([[1, 2], matrix([3, 4])], 2)'],
	  seealso: ['subtract', 'partitionSelect']
	};
	return diff;
}

var dot = {};

var hasRequiredDot;

function requireDot () {
	if (hasRequiredDot) return dot;
	hasRequiredDot = 1;

	Object.defineProperty(dot, "__esModule", {
	  value: true
	});
	dot.dotDocs = void 0;
	dot.dotDocs = {
	  name: 'dot',
	  category: 'Matrix',
	  syntax: ['dot(A, B)', 'A * B'],
	  description: 'Calculate the dot product of two vectors. ' + 'The dot product of A = [a1, a2, a3, ..., an] and B = [b1, b2, b3, ..., bn] ' + 'is defined as dot(A, B) = a1 * b1 + a2 * b2 + a3 * b3 + ... + an * bn',
	  examples: ['dot([2, 4, 1], [2, 2, 3])', '[2, 4, 1] * [2, 2, 3]'],
	  seealso: ['multiply', 'cross']
	};
	return dot;
}

var eigs$1 = {};

var hasRequiredEigs$1;

function requireEigs$1 () {
	if (hasRequiredEigs$1) return eigs$1;
	hasRequiredEigs$1 = 1;

	Object.defineProperty(eigs$1, "__esModule", {
	  value: true
	});
	eigs$1.eigsDocs = void 0;
	eigs$1.eigsDocs = {
	  name: 'eigs',
	  category: 'Matrix',
	  syntax: ['eigs(x)'],
	  description: 'Calculate the eigenvalues and optionally eigenvectors of a square matrix',
	  examples: ['eigs([[5, 2.3], [2.3, 1]])', 'eigs([[1, 2, 3], [4, 5, 6], [7, 8, 9]], { precision: 1e-6, eigenvectors: false })'],
	  seealso: ['inv']
	};
	return eigs$1;
}

var filter = {};

var hasRequiredFilter;

function requireFilter () {
	if (hasRequiredFilter) return filter;
	hasRequiredFilter = 1;

	Object.defineProperty(filter, "__esModule", {
	  value: true
	});
	filter.filterDocs = void 0;
	filter.filterDocs = {
	  name: 'filter',
	  category: 'Matrix',
	  syntax: ['filter(x, test)'],
	  description: 'Filter items in a matrix.',
	  examples: ['isPositive(x) = x > 0', 'filter([6, -2, -1, 4, 3], isPositive)', 'filter([6, -2, 0, 1, 0], x != 0)'],
	  seealso: ['sort', 'map', 'forEach']
	};
	return filter;
}

var flatten = {};

var hasRequiredFlatten;

function requireFlatten () {
	if (hasRequiredFlatten) return flatten;
	hasRequiredFlatten = 1;

	Object.defineProperty(flatten, "__esModule", {
	  value: true
	});
	flatten.flattenDocs = void 0;
	flatten.flattenDocs = {
	  name: 'flatten',
	  category: 'Matrix',
	  syntax: ['flatten(x)'],
	  description: 'Flatten a multi dimensional matrix into a single dimensional matrix.',
	  examples: ['a = [1, 2, 3; 4, 5, 6]', 'size(a)', 'b = flatten(a)', 'size(b)'],
	  seealso: ['concat', 'resize', 'size', 'squeeze']
	};
	return flatten;
}

var forEach$1 = {};

var hasRequiredForEach;

function requireForEach () {
	if (hasRequiredForEach) return forEach$1;
	hasRequiredForEach = 1;

	Object.defineProperty(forEach$1, "__esModule", {
	  value: true
	});
	forEach$1.forEachDocs = void 0;
	forEach$1.forEachDocs = {
	  name: 'forEach',
	  category: 'Matrix',
	  syntax: ['forEach(x, callback)'],
	  description: 'Iterates over all elements of a matrix/array, and executes the given callback function.',
	  examples: ['numberOfPets = {}', 'addPet(n) = numberOfPets[n] = (numberOfPets[n] ? numberOfPets[n]:0 ) + 1;', 'forEach(["Dog","Cat","Cat"], addPet)', 'numberOfPets'],
	  seealso: ['map', 'sort', 'filter']
	};
	return forEach$1;
}

var getMatrixDataType = {};

var hasRequiredGetMatrixDataType;

function requireGetMatrixDataType () {
	if (hasRequiredGetMatrixDataType) return getMatrixDataType;
	hasRequiredGetMatrixDataType = 1;

	Object.defineProperty(getMatrixDataType, "__esModule", {
	  value: true
	});
	getMatrixDataType.getMatrixDataTypeDocs = void 0;
	getMatrixDataType.getMatrixDataTypeDocs = {
	  name: 'getMatrixDataType',
	  category: 'Matrix',
	  syntax: ['getMatrixDataType(x)'],
	  description: 'Find the data type of all elements in a matrix or array, ' + 'for example "number" if all items are a number ' + 'and "Complex" if all values are complex numbers. ' + 'If a matrix contains more than one data type, it will return "mixed".',
	  examples: ['getMatrixDataType([1, 2, 3])', 'getMatrixDataType([[5 cm], [2 inch]])', 'getMatrixDataType([1, "text"])', 'getMatrixDataType([1, bignumber(4)])'],
	  seealso: ['matrix', 'sparse', 'typeOf']
	};
	return getMatrixDataType;
}

var identity = {};

var hasRequiredIdentity;

function requireIdentity () {
	if (hasRequiredIdentity) return identity;
	hasRequiredIdentity = 1;

	Object.defineProperty(identity, "__esModule", {
	  value: true
	});
	identity.identityDocs = void 0;
	identity.identityDocs = {
	  name: 'identity',
	  category: 'Matrix',
	  syntax: ['identity(n)', 'identity(m, n)', 'identity([m, n])'],
	  description: 'Returns the identity matrix with size m-by-n. The matrix has ones on the diagonal and zeros elsewhere.',
	  examples: ['identity(3)', 'identity(3, 5)', 'a = [1, 2, 3; 4, 5, 6]', 'identity(size(a))'],
	  seealso: ['concat', 'det', 'diag', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return identity;
}

var inv$1 = {};

var hasRequiredInv$1;

function requireInv$1 () {
	if (hasRequiredInv$1) return inv$1;
	hasRequiredInv$1 = 1;

	Object.defineProperty(inv$1, "__esModule", {
	  value: true
	});
	inv$1.invDocs = void 0;
	inv$1.invDocs = {
	  name: 'inv',
	  category: 'Matrix',
	  syntax: ['inv(x)'],
	  description: 'Calculate the inverse of a matrix',
	  examples: ['inv([1, 2; 3, 4])', 'inv(4)', '1 / 4'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return inv$1;
}

var pinv$1 = {};

var hasRequiredPinv$1;

function requirePinv$1 () {
	if (hasRequiredPinv$1) return pinv$1;
	hasRequiredPinv$1 = 1;

	Object.defineProperty(pinv$1, "__esModule", {
	  value: true
	});
	pinv$1.pinvDocs = void 0;
	pinv$1.pinvDocs = {
	  name: 'pinv',
	  category: 'Matrix',
	  syntax: ['pinv(x)'],
	  description: 'Calculate the Moore–Penrose inverse of a matrix',
	  examples: ['pinv([1, 2; 3, 4])', 'pinv([[1, 0], [0, 1], [0, 1]])', 'pinv(4)'],
	  seealso: ['inv']
	};
	return pinv$1;
}

var kron = {};

var hasRequiredKron;

function requireKron () {
	if (hasRequiredKron) return kron;
	hasRequiredKron = 1;

	Object.defineProperty(kron, "__esModule", {
	  value: true
	});
	kron.kronDocs = void 0;
	kron.kronDocs = {
	  name: 'kron',
	  category: 'Matrix',
	  syntax: ['kron(x, y)'],
	  description: 'Calculates the Kronecker product of 2 matrices or vectors.',
	  examples: ['kron([[1, 0], [0, 1]], [[1, 2], [3, 4]])', 'kron([1,1], [2,3,4])'],
	  seealso: ['multiply', 'dot', 'cross']
	};
	return kron;
}

var map = {};

var hasRequiredMap;

function requireMap () {
	if (hasRequiredMap) return map;
	hasRequiredMap = 1;

	Object.defineProperty(map, "__esModule", {
	  value: true
	});
	map.mapDocs = void 0;
	map.mapDocs = {
	  name: 'map',
	  category: 'Matrix',
	  syntax: ['map(x, callback)', 'map(x, y, ..., callback)'],
	  description: 'Create a new matrix or array with the results of the callback function executed on each entry of the matrix/array or the matrices/arrays.',
	  examples: ['map([1, 2, 3], square)', 'map([1, 2], [3, 4], f(a,b) = a + b)'],
	  seealso: ['filter', 'forEach']
	};
	return map;
}

var matrixFromColumns = {};

var hasRequiredMatrixFromColumns;

function requireMatrixFromColumns () {
	if (hasRequiredMatrixFromColumns) return matrixFromColumns;
	hasRequiredMatrixFromColumns = 1;

	Object.defineProperty(matrixFromColumns, "__esModule", {
	  value: true
	});
	matrixFromColumns.matrixFromColumnsDocs = void 0;
	matrixFromColumns.matrixFromColumnsDocs = {
	  name: 'matrixFromColumns',
	  category: 'Matrix',
	  syntax: ['matrixFromColumns(...arr)', 'matrixFromColumns(row1, row2)', 'matrixFromColumns(row1, row2, row3)'],
	  description: 'Create a dense matrix from vectors as individual columns.',
	  examples: ['matrixFromColumns([1, 2, 3], [[4],[5],[6]])'],
	  seealso: ['matrix', 'matrixFromRows', 'matrixFromFunction', 'zeros']
	};
	return matrixFromColumns;
}

var matrixFromFunction = {};

var hasRequiredMatrixFromFunction;

function requireMatrixFromFunction () {
	if (hasRequiredMatrixFromFunction) return matrixFromFunction;
	hasRequiredMatrixFromFunction = 1;

	Object.defineProperty(matrixFromFunction, "__esModule", {
	  value: true
	});
	matrixFromFunction.matrixFromFunctionDocs = void 0;
	matrixFromFunction.matrixFromFunctionDocs = {
	  name: 'matrixFromFunction',
	  category: 'Matrix',
	  syntax: ['matrixFromFunction(size, fn)', 'matrixFromFunction(size, fn, format)', 'matrixFromFunction(size, fn, format, datatype)', 'matrixFromFunction(size, format, fn)', 'matrixFromFunction(size, format, datatype, fn)'],
	  description: 'Create a matrix by evaluating a generating function at each index.',
	  examples: ['f(I) = I[1] - I[2]', 'matrixFromFunction([3,3], f)', 'g(I) = I[1] - I[2] == 1 ? 4 : 0', 'matrixFromFunction([100, 100], "sparse", g)', 'matrixFromFunction([5], random)'],
	  seealso: ['matrix', 'matrixFromRows', 'matrixFromColumns', 'zeros']
	};
	return matrixFromFunction;
}

var matrixFromRows = {};

var hasRequiredMatrixFromRows;

function requireMatrixFromRows () {
	if (hasRequiredMatrixFromRows) return matrixFromRows;
	hasRequiredMatrixFromRows = 1;

	Object.defineProperty(matrixFromRows, "__esModule", {
	  value: true
	});
	matrixFromRows.matrixFromRowsDocs = void 0;
	matrixFromRows.matrixFromRowsDocs = {
	  name: 'matrixFromRows',
	  category: 'Matrix',
	  syntax: ['matrixFromRows(...arr)', 'matrixFromRows(row1, row2)', 'matrixFromRows(row1, row2, row3)'],
	  description: 'Create a dense matrix from vectors as individual rows.',
	  examples: ['matrixFromRows([1, 2, 3], [[4],[5],[6]])'],
	  seealso: ['matrix', 'matrixFromColumns', 'matrixFromFunction', 'zeros']
	};
	return matrixFromRows;
}

var ones = {};

var hasRequiredOnes;

function requireOnes () {
	if (hasRequiredOnes) return ones;
	hasRequiredOnes = 1;

	Object.defineProperty(ones, "__esModule", {
	  value: true
	});
	ones.onesDocs = void 0;
	ones.onesDocs = {
	  name: 'ones',
	  category: 'Matrix',
	  syntax: ['ones(m)', 'ones(m, n)', 'ones(m, n, p, ...)', 'ones([m])', 'ones([m, n])', 'ones([m, n, p, ...])'],
	  description: 'Create a matrix containing ones.',
	  examples: ['ones(3)', 'ones(3, 5)', 'ones([2,3]) * 4.5', 'a = [1, 2, 3; 4, 5, 6]', 'ones(size(a))'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return ones;
}

var partitionSelect = {};

var hasRequiredPartitionSelect;

function requirePartitionSelect () {
	if (hasRequiredPartitionSelect) return partitionSelect;
	hasRequiredPartitionSelect = 1;

	Object.defineProperty(partitionSelect, "__esModule", {
	  value: true
	});
	partitionSelect.partitionSelectDocs = void 0;
	partitionSelect.partitionSelectDocs = {
	  name: 'partitionSelect',
	  category: 'Matrix',
	  syntax: ['partitionSelect(x, k)', 'partitionSelect(x, k, compare)'],
	  description: 'Partition-based selection of an array or 1D matrix. Will find the kth smallest value, and mutates the input array. Uses Quickselect.',
	  examples: ['partitionSelect([5, 10, 1], 2)', 'partitionSelect(["C", "B", "A", "D"], 1, compareText)', 'arr = [5, 2, 1]', 'partitionSelect(arr, 0) # returns 1, arr is now: [1, 2, 5]', 'arr', 'partitionSelect(arr, 1, \'desc\') # returns 2, arr is now: [5, 2, 1]', 'arr'],
	  seealso: ['sort']
	};
	return partitionSelect;
}

var range = {};

var hasRequiredRange;

function requireRange () {
	if (hasRequiredRange) return range;
	hasRequiredRange = 1;

	Object.defineProperty(range, "__esModule", {
	  value: true
	});
	range.rangeDocs = void 0;
	range.rangeDocs = {
	  name: 'range',
	  category: 'Type',
	  syntax: ['start:end', 'start:step:end', 'range(start, end)', 'range(start, end, step)', 'range(string)'],
	  description: 'Create a range. Lower bound of the range is included, upper bound is excluded.',
	  examples: ['1:5', '3:-1:-3', 'range(3, 7)', 'range(0, 12, 2)', 'range("4:10")', 'range(1m, 1m, 3m)', 'a = [1, 2, 3, 4; 5, 6, 7, 8]', 'a[1:2, 1:2]'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'size', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return range;
}

var reshape = {};

var hasRequiredReshape;

function requireReshape () {
	if (hasRequiredReshape) return reshape;
	hasRequiredReshape = 1;

	Object.defineProperty(reshape, "__esModule", {
	  value: true
	});
	reshape.reshapeDocs = void 0;
	reshape.reshapeDocs = {
	  name: 'reshape',
	  category: 'Matrix',
	  syntax: ['reshape(x, sizes)'],
	  description: 'Reshape a multi dimensional array to fit the specified dimensions.',
	  examples: ['reshape([1, 2, 3, 4, 5, 6], [2, 3])', 'reshape([[1, 2], [3, 4]], [1, 4])', 'reshape([[1, 2], [3, 4]], [4])', 'reshape([1, 2, 3, 4], [-1, 2])'],
	  seealso: ['size', 'squeeze', 'resize']
	};
	return reshape;
}

var resize = {};

var hasRequiredResize;

function requireResize () {
	if (hasRequiredResize) return resize;
	hasRequiredResize = 1;

	Object.defineProperty(resize, "__esModule", {
	  value: true
	});
	resize.resizeDocs = void 0;
	resize.resizeDocs = {
	  name: 'resize',
	  category: 'Matrix',
	  syntax: ['resize(x, size)', 'resize(x, size, defaultValue)'],
	  description: 'Resize a matrix.',
	  examples: ['resize([1,2,3,4,5], [3])', 'resize([1,2,3], [5])', 'resize([1,2,3], [5], -1)', 'resize(2, [2, 3])', 'resize("hello", [8], "!")'],
	  seealso: ['size', 'subset', 'squeeze', 'reshape']
	};
	return resize;
}

var rotate = {};

var hasRequiredRotate;

function requireRotate () {
	if (hasRequiredRotate) return rotate;
	hasRequiredRotate = 1;

	Object.defineProperty(rotate, "__esModule", {
	  value: true
	});
	rotate.rotateDocs = void 0;
	rotate.rotateDocs = {
	  name: 'rotate',
	  category: 'Matrix',
	  syntax: ['rotate(w, theta)', 'rotate(w, theta, v)'],
	  description: 'Returns a 2-D rotation matrix (2x2) for a given angle (in radians). ' + 'Returns a 2-D rotation matrix (3x3) of a given angle (in radians) around given axis.',
	  examples: ['rotate([1, 0], pi / 2)', 'rotate(matrix([1, 0]), unit("35deg"))', 'rotate([1, 0, 0], unit("90deg"), [0, 0, 1])', 'rotate(matrix([1, 0, 0]), unit("90deg"), matrix([0, 0, 1]))'],
	  seealso: ['matrix', 'rotationMatrix']
	};
	return rotate;
}

var rotationMatrix = {};

var hasRequiredRotationMatrix;

function requireRotationMatrix () {
	if (hasRequiredRotationMatrix) return rotationMatrix;
	hasRequiredRotationMatrix = 1;

	Object.defineProperty(rotationMatrix, "__esModule", {
	  value: true
	});
	rotationMatrix.rotationMatrixDocs = void 0;
	rotationMatrix.rotationMatrixDocs = {
	  name: 'rotationMatrix',
	  category: 'Matrix',
	  syntax: ['rotationMatrix(theta)', 'rotationMatrix(theta, v)', 'rotationMatrix(theta, v, format)'],
	  description: 'Returns a 2-D rotation matrix (2x2) for a given angle (in radians). ' + 'Returns a 2-D rotation matrix (3x3) of a given angle (in radians) around given axis.',
	  examples: ['rotationMatrix(pi / 2)', 'rotationMatrix(unit("45deg"), [0, 0, 1])', 'rotationMatrix(1, matrix([0, 0, 1]), "sparse")'],
	  seealso: ['cos', 'sin']
	};
	return rotationMatrix;
}

var row = {};

var hasRequiredRow;

function requireRow () {
	if (hasRequiredRow) return row;
	hasRequiredRow = 1;

	Object.defineProperty(row, "__esModule", {
	  value: true
	});
	row.rowDocs = void 0;
	row.rowDocs = {
	  name: 'row',
	  category: 'Matrix',
	  syntax: ['row(x, index)'],
	  description: 'Return a row from a matrix or array.',
	  examples: ['A = [[1, 2], [3, 4]]', 'row(A, 1)', 'row(A, 2)'],
	  seealso: ['column', 'matrixFromRows']
	};
	return row;
}

var size = {};

var hasRequiredSize;

function requireSize () {
	if (hasRequiredSize) return size;
	hasRequiredSize = 1;

	Object.defineProperty(size, "__esModule", {
	  value: true
	});
	size.sizeDocs = void 0;
	size.sizeDocs = {
	  name: 'size',
	  category: 'Matrix',
	  syntax: ['size(x)'],
	  description: 'Calculate the size of a matrix.',
	  examples: ['size(2.3)', 'size("hello world")', 'a = [1, 2; 3, 4; 5, 6]', 'size(a)', 'size(1:6)'],
	  seealso: ['concat', 'count', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'squeeze', 'subset', 'trace', 'transpose', 'zeros']
	};
	return size;
}

var sort = {};

var hasRequiredSort;

function requireSort () {
	if (hasRequiredSort) return sort;
	hasRequiredSort = 1;

	Object.defineProperty(sort, "__esModule", {
	  value: true
	});
	sort.sortDocs = void 0;
	sort.sortDocs = {
	  name: 'sort',
	  category: 'Matrix',
	  syntax: ['sort(x)', 'sort(x, compare)'],
	  description: 'Sort the items in a matrix. Compare can be a string "asc", "desc", "natural", or a custom sort function.',
	  examples: ['sort([5, 10, 1])', 'sort(["C", "B", "A", "D"], "natural")', 'sortByLength(a, b) = size(a)[1] - size(b)[1]', 'sort(["Langdon", "Tom", "Sara"], sortByLength)', 'sort(["10", "1", "2"], "natural")'],
	  seealso: ['map', 'filter', 'forEach']
	};
	return sort;
}

var squeeze = {};

var hasRequiredSqueeze;

function requireSqueeze () {
	if (hasRequiredSqueeze) return squeeze;
	hasRequiredSqueeze = 1;

	Object.defineProperty(squeeze, "__esModule", {
	  value: true
	});
	squeeze.squeezeDocs = void 0;
	squeeze.squeezeDocs = {
	  name: 'squeeze',
	  category: 'Matrix',
	  syntax: ['squeeze(x)'],
	  description: 'Remove inner and outer singleton dimensions from a matrix.',
	  examples: ['a = zeros(3,2,1)', 'size(squeeze(a))', 'b = zeros(1,1,3)', 'size(squeeze(b))'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'subset', 'trace', 'transpose', 'zeros']
	};
	return squeeze;
}

var subset = {};

var hasRequiredSubset;

function requireSubset () {
	if (hasRequiredSubset) return subset;
	hasRequiredSubset = 1;

	Object.defineProperty(subset, "__esModule", {
	  value: true
	});
	subset.subsetDocs = void 0;
	subset.subsetDocs = {
	  name: 'subset',
	  category: 'Matrix',
	  syntax: ['value(index)', 'value(index) = replacement', 'subset(value, [index])', 'subset(value, [index], replacement)'],
	  description: 'Get or set a subset of the entries of a matrix or ' + 'characters of a string. ' + 'Indexes are one-based. There should be one index specification for ' + 'each dimension of the target. Each specification can be a single ' + 'index, a list of indices, or a range in colon notation `l:u`. ' + 'In a range, both the lower bound l and upper bound u are included; ' + 'and if a bound is omitted it defaults to the most extreme valid value. ' + 'The cartesian product of the indices specified in each dimension ' + 'determines the target of the operation.',
	  examples: ['d = [1, 2; 3, 4]', 'e = []', 'e[1, 1:2] = [5, 6]', 'e[2, :] = [7, 8]', 'f = d * e', 'f[2, 1]', 'f[:, 1]', 'f[[1,2], [1,3]] = [9, 10; 11, 12]', 'f'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'trace', 'transpose', 'zeros']
	};
	return subset;
}

var trace = {};

var hasRequiredTrace;

function requireTrace () {
	if (hasRequiredTrace) return trace;
	hasRequiredTrace = 1;

	Object.defineProperty(trace, "__esModule", {
	  value: true
	});
	trace.traceDocs = void 0;
	trace.traceDocs = {
	  name: 'trace',
	  category: 'Matrix',
	  syntax: ['trace(A)'],
	  description: 'Calculate the trace of a matrix: the sum of the elements on the main diagonal of a square matrix.',
	  examples: ['A = [1, 2, 3; -1, 2, 3; 2, 0, 3]', 'trace(A)'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'transpose', 'zeros']
	};
	return trace;
}

var transpose = {};

var hasRequiredTranspose;

function requireTranspose () {
	if (hasRequiredTranspose) return transpose;
	hasRequiredTranspose = 1;

	Object.defineProperty(transpose, "__esModule", {
	  value: true
	});
	transpose.transposeDocs = void 0;
	transpose.transposeDocs = {
	  name: 'transpose',
	  category: 'Matrix',
	  syntax: ['x\'', 'transpose(x)'],
	  description: 'Transpose a matrix',
	  examples: ['a = [1, 2, 3; 4, 5, 6]', 'a\'', 'transpose(a)'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'zeros']
	};
	return transpose;
}

var zeros = {};

var hasRequiredZeros;

function requireZeros () {
	if (hasRequiredZeros) return zeros;
	hasRequiredZeros = 1;

	Object.defineProperty(zeros, "__esModule", {
	  value: true
	});
	zeros.zerosDocs = void 0;
	zeros.zerosDocs = {
	  name: 'zeros',
	  category: 'Matrix',
	  syntax: ['zeros(m)', 'zeros(m, n)', 'zeros(m, n, p, ...)', 'zeros([m])', 'zeros([m, n])', 'zeros([m, n, p, ...])'],
	  description: 'Create a matrix containing zeros.',
	  examples: ['zeros(3)', 'zeros(3, 5)', 'a = [1, 2, 3; 4, 5, 6]', 'zeros(size(a))'],
	  seealso: ['concat', 'det', 'diag', 'identity', 'inv', 'ones', 'range', 'size', 'squeeze', 'subset', 'trace', 'transpose']
	};
	return zeros;
}

var fft = {};

var hasRequiredFft;

function requireFft () {
	if (hasRequiredFft) return fft;
	hasRequiredFft = 1;

	Object.defineProperty(fft, "__esModule", {
	  value: true
	});
	fft.fftDocs = void 0;
	fft.fftDocs = {
	  name: 'fft',
	  category: 'Matrix',
	  syntax: ['fft(x)'],
	  description: 'Calculate N-dimensional Fourier transform',
	  examples: ['fft([[1, 0], [1, 0]])'],
	  seealso: ['ifft']
	};
	return fft;
}

var ifft = {};

var hasRequiredIfft;

function requireIfft () {
	if (hasRequiredIfft) return ifft;
	hasRequiredIfft = 1;

	Object.defineProperty(ifft, "__esModule", {
	  value: true
	});
	ifft.ifftDocs = void 0;
	ifft.ifftDocs = {
	  name: 'ifft',
	  category: 'Matrix',
	  syntax: ['ifft(x)'],
	  description: 'Calculate N-dimensional inverse Fourier transform',
	  examples: ['ifft([[2, 2], [0, 0]])'],
	  seealso: ['fft']
	};
	return ifft;
}

var bernoulli$1 = {};

var hasRequiredBernoulli$1;

function requireBernoulli$1 () {
	if (hasRequiredBernoulli$1) return bernoulli$1;
	hasRequiredBernoulli$1 = 1;

	Object.defineProperty(bernoulli$1, "__esModule", {
	  value: true
	});
	bernoulli$1.bernoulliDocs = void 0;
	bernoulli$1.bernoulliDocs = {
	  name: 'bernoulli',
	  category: 'Probability',
	  syntax: ['bernoulli(n)'],
	  description: 'The nth Bernoulli number',
	  examples: ['bernoulli(4)', 'bernoulli(fraction(12))'],
	  seealso: ['combinations', 'gamma', 'stirlingS2']
	};
	return bernoulli$1;
}

var combinations$1 = {};

var hasRequiredCombinations$1;

function requireCombinations$1 () {
	if (hasRequiredCombinations$1) return combinations$1;
	hasRequiredCombinations$1 = 1;

	Object.defineProperty(combinations$1, "__esModule", {
	  value: true
	});
	combinations$1.combinationsDocs = void 0;
	combinations$1.combinationsDocs = {
	  name: 'combinations',
	  category: 'Probability',
	  syntax: ['combinations(n, k)'],
	  description: 'Compute the number of combinations of n items taken k at a time',
	  examples: ['combinations(7, 5)'],
	  seealso: ['combinationsWithRep', 'permutations', 'factorial']
	};
	return combinations$1;
}

var combinationsWithRep$1 = {};

var hasRequiredCombinationsWithRep$1;

function requireCombinationsWithRep$1 () {
	if (hasRequiredCombinationsWithRep$1) return combinationsWithRep$1;
	hasRequiredCombinationsWithRep$1 = 1;

	Object.defineProperty(combinationsWithRep$1, "__esModule", {
	  value: true
	});
	combinationsWithRep$1.combinationsWithRepDocs = void 0;
	combinationsWithRep$1.combinationsWithRepDocs = {
	  name: 'combinationsWithRep',
	  category: 'Probability',
	  syntax: ['combinationsWithRep(n, k)'],
	  description: 'Compute the number of combinations of n items taken k at a time with replacements.',
	  examples: ['combinationsWithRep(7, 5)'],
	  seealso: ['combinations', 'permutations', 'factorial']
	};
	return combinationsWithRep$1;
}

var factorial$1 = {};

var hasRequiredFactorial$1;

function requireFactorial$1 () {
	if (hasRequiredFactorial$1) return factorial$1;
	hasRequiredFactorial$1 = 1;

	Object.defineProperty(factorial$1, "__esModule", {
	  value: true
	});
	factorial$1.factorialDocs = void 0;
	factorial$1.factorialDocs = {
	  name: 'factorial',
	  category: 'Probability',
	  syntax: ['n!', 'factorial(n)'],
	  description: 'Compute the factorial of a value',
	  examples: ['5!', '5 * 4 * 3 * 2 * 1', '3!'],
	  seealso: ['combinations', 'combinationsWithRep', 'permutations', 'gamma']
	};
	return factorial$1;
}

var gamma$1 = {};

var hasRequiredGamma$1;

function requireGamma$1 () {
	if (hasRequiredGamma$1) return gamma$1;
	hasRequiredGamma$1 = 1;

	Object.defineProperty(gamma$1, "__esModule", {
	  value: true
	});
	gamma$1.gammaDocs = void 0;
	gamma$1.gammaDocs = {
	  name: 'gamma',
	  category: 'Probability',
	  syntax: ['gamma(n)'],
	  description: 'Compute the gamma function. For small values, the Lanczos approximation is used, and for large values the extended Stirling approximation.',
	  examples: ['gamma(4)', '3!', 'gamma(1/2)', 'sqrt(pi)'],
	  seealso: ['factorial']
	};
	return gamma$1;
}

var lgamma$1 = {};

var hasRequiredLgamma$1;

function requireLgamma$1 () {
	if (hasRequiredLgamma$1) return lgamma$1;
	hasRequiredLgamma$1 = 1;

	Object.defineProperty(lgamma$1, "__esModule", {
	  value: true
	});
	lgamma$1.lgammaDocs = void 0;
	lgamma$1.lgammaDocs = {
	  name: 'lgamma',
	  category: 'Probability',
	  syntax: ['lgamma(n)'],
	  description: 'Logarithm of the gamma function for real, positive numbers and complex numbers, ' + 'using Lanczos approximation for numbers and Stirling series for complex numbers.',
	  examples: ['lgamma(4)', 'lgamma(1/2)', 'lgamma(i)', 'lgamma(complex(1.1, 2))'],
	  seealso: ['gamma']
	};
	return lgamma$1;
}

var kldivergence$1 = {};

var hasRequiredKldivergence$1;

function requireKldivergence$1 () {
	if (hasRequiredKldivergence$1) return kldivergence$1;
	hasRequiredKldivergence$1 = 1;

	Object.defineProperty(kldivergence$1, "__esModule", {
	  value: true
	});
	kldivergence$1.kldivergenceDocs = void 0;
	kldivergence$1.kldivergenceDocs = {
	  name: 'kldivergence',
	  category: 'Probability',
	  syntax: ['kldivergence(x, y)'],
	  description: 'Calculate the Kullback-Leibler (KL) divergence  between two distributions.',
	  examples: ['kldivergence([0.7,0.5,0.4], [0.2,0.9,0.5])'],
	  seealso: []
	};
	return kldivergence$1;
}

var multinomial$1 = {};

var hasRequiredMultinomial$1;

function requireMultinomial$1 () {
	if (hasRequiredMultinomial$1) return multinomial$1;
	hasRequiredMultinomial$1 = 1;

	Object.defineProperty(multinomial$1, "__esModule", {
	  value: true
	});
	multinomial$1.multinomialDocs = void 0;
	multinomial$1.multinomialDocs = {
	  name: 'multinomial',
	  category: 'Probability',
	  syntax: ['multinomial(A)'],
	  description: 'Multinomial Coefficients compute the number of ways of picking a1, a2, ..., ai unordered outcomes from `n` possibilities. multinomial takes one array of integers as an argument. The following condition must be enforced: every ai > 0.',
	  examples: ['multinomial([1, 2, 1])'],
	  seealso: ['combinations', 'factorial']
	};
	return multinomial$1;
}

var permutations$1 = {};

var hasRequiredPermutations$1;

function requirePermutations$1 () {
	if (hasRequiredPermutations$1) return permutations$1;
	hasRequiredPermutations$1 = 1;

	Object.defineProperty(permutations$1, "__esModule", {
	  value: true
	});
	permutations$1.permutationsDocs = void 0;
	permutations$1.permutationsDocs = {
	  name: 'permutations',
	  category: 'Probability',
	  syntax: ['permutations(n)', 'permutations(n, k)'],
	  description: 'Compute the number of permutations of n items taken k at a time',
	  examples: ['permutations(5)', 'permutations(5, 3)'],
	  seealso: ['combinations', 'combinationsWithRep', 'factorial']
	};
	return permutations$1;
}

var pickRandom$1 = {};

var hasRequiredPickRandom$1;

function requirePickRandom$1 () {
	if (hasRequiredPickRandom$1) return pickRandom$1;
	hasRequiredPickRandom$1 = 1;

	Object.defineProperty(pickRandom$1, "__esModule", {
	  value: true
	});
	pickRandom$1.pickRandomDocs = void 0;
	pickRandom$1.pickRandomDocs = {
	  name: 'pickRandom',
	  category: 'Probability',
	  syntax: ['pickRandom(array)', 'pickRandom(array, number)', 'pickRandom(array, weights)', 'pickRandom(array, number, weights)', 'pickRandom(array, weights, number)'],
	  description: 'Pick a random entry from a given array.',
	  examples: ['pickRandom(0:10)', 'pickRandom([1, 3, 1, 6])', 'pickRandom([1, 3, 1, 6], 2)', 'pickRandom([1, 3, 1, 6], [2, 3, 2, 1])', 'pickRandom([1, 3, 1, 6], 2, [2, 3, 2, 1])', 'pickRandom([1, 3, 1, 6], [2, 3, 2, 1], 2)'],
	  seealso: ['random', 'randomInt']
	};
	return pickRandom$1;
}

var random$1 = {};

var hasRequiredRandom$1;

function requireRandom$1 () {
	if (hasRequiredRandom$1) return random$1;
	hasRequiredRandom$1 = 1;

	Object.defineProperty(random$1, "__esModule", {
	  value: true
	});
	random$1.randomDocs = void 0;
	random$1.randomDocs = {
	  name: 'random',
	  category: 'Probability',
	  syntax: ['random()', 'random(max)', 'random(min, max)', 'random(size)', 'random(size, max)', 'random(size, min, max)'],
	  description: 'Return a random number.',
	  examples: ['random()', 'random(10, 20)', 'random([2, 3])'],
	  seealso: ['pickRandom', 'randomInt']
	};
	return random$1;
}

var randomInt$1 = {};

var hasRequiredRandomInt$1;

function requireRandomInt$1 () {
	if (hasRequiredRandomInt$1) return randomInt$1;
	hasRequiredRandomInt$1 = 1;

	Object.defineProperty(randomInt$1, "__esModule", {
	  value: true
	});
	randomInt$1.randomIntDocs = void 0;
	randomInt$1.randomIntDocs = {
	  name: 'randomInt',
	  category: 'Probability',
	  syntax: ['randomInt(max)', 'randomInt(min, max)', 'randomInt(size)', 'randomInt(size, max)', 'randomInt(size, min, max)'],
	  description: 'Return a random integer number',
	  examples: ['randomInt(10, 20)', 'randomInt([2, 3], 10)'],
	  seealso: ['pickRandom', 'random']
	};
	return randomInt$1;
}

var compare = {};

var hasRequiredCompare;

function requireCompare () {
	if (hasRequiredCompare) return compare;
	hasRequiredCompare = 1;

	Object.defineProperty(compare, "__esModule", {
	  value: true
	});
	compare.compareDocs = void 0;
	compare.compareDocs = {
	  name: 'compare',
	  category: 'Relational',
	  syntax: ['compare(x, y)'],
	  description: 'Compare two values. ' + 'Returns 1 when x > y, -1 when x < y, and 0 when x == y.',
	  examples: ['compare(2, 3)', 'compare(3, 2)', 'compare(2, 2)', 'compare(5cm, 40mm)', 'compare(2, [1, 2, 3])'],
	  seealso: ['equal', 'unequal', 'smaller', 'smallerEq', 'largerEq', 'compareNatural', 'compareText']
	};
	return compare;
}

var compareNatural = {};

var hasRequiredCompareNatural;

function requireCompareNatural () {
	if (hasRequiredCompareNatural) return compareNatural;
	hasRequiredCompareNatural = 1;

	Object.defineProperty(compareNatural, "__esModule", {
	  value: true
	});
	compareNatural.compareNaturalDocs = void 0;
	compareNatural.compareNaturalDocs = {
	  name: 'compareNatural',
	  category: 'Relational',
	  syntax: ['compareNatural(x, y)'],
	  description: 'Compare two values of any type in a deterministic, natural way. ' + 'Returns 1 when x > y, -1 when x < y, and 0 when x == y.',
	  examples: ['compareNatural(2, 3)', 'compareNatural(3, 2)', 'compareNatural(2, 2)', 'compareNatural(5cm, 40mm)', 'compareNatural("2", "10")', 'compareNatural(2 + 3i, 2 + 4i)', 'compareNatural([1, 2, 4], [1, 2, 3])', 'compareNatural([1, 5], [1, 2, 3])', 'compareNatural([1, 2], [1, 2])', 'compareNatural({a: 2}, {a: 4})'],
	  seealso: ['equal', 'unequal', 'smaller', 'smallerEq', 'largerEq', 'compare', 'compareText']
	};
	return compareNatural;
}

var compareText = {};

var hasRequiredCompareText;

function requireCompareText () {
	if (hasRequiredCompareText) return compareText;
	hasRequiredCompareText = 1;

	Object.defineProperty(compareText, "__esModule", {
	  value: true
	});
	compareText.compareTextDocs = void 0;
	compareText.compareTextDocs = {
	  name: 'compareText',
	  category: 'Relational',
	  syntax: ['compareText(x, y)'],
	  description: 'Compare two strings lexically. Comparison is case sensitive. ' + 'Returns 1 when x > y, -1 when x < y, and 0 when x == y.',
	  examples: ['compareText("B", "A")', 'compareText("A", "B")', 'compareText("A", "A")', 'compareText("2", "10")', 'compare("2", "10")', 'compare(2, 10)', 'compareNatural("2", "10")', 'compareText("B", ["A", "B", "C"])'],
	  seealso: ['compare', 'compareNatural']
	};
	return compareText;
}

var deepEqual = {};

var hasRequiredDeepEqual;

function requireDeepEqual () {
	if (hasRequiredDeepEqual) return deepEqual;
	hasRequiredDeepEqual = 1;

	Object.defineProperty(deepEqual, "__esModule", {
	  value: true
	});
	deepEqual.deepEqualDocs = void 0;
	deepEqual.deepEqualDocs = {
	  name: 'deepEqual',
	  category: 'Relational',
	  syntax: ['deepEqual(x, y)'],
	  description: 'Check equality of two matrices element wise. Returns true if the size of both matrices is equal and when and each of the elements are equal.',
	  examples: ['deepEqual([1,3,4], [1,3,4])', 'deepEqual([1,3,4], [1,3])'],
	  seealso: ['equal', 'unequal', 'smaller', 'larger', 'smallerEq', 'largerEq', 'compare']
	};
	return deepEqual;
}

var equal$1 = {};

var hasRequiredEqual;

function requireEqual () {
	if (hasRequiredEqual) return equal$1;
	hasRequiredEqual = 1;

	Object.defineProperty(equal$1, "__esModule", {
	  value: true
	});
	equal$1.equalDocs = void 0;
	equal$1.equalDocs = {
	  name: 'equal',
	  category: 'Relational',
	  syntax: ['x == y', 'equal(x, y)'],
	  description: 'Check equality of two values. Returns true if the values are equal, and false if not.',
	  examples: ['2+2 == 3', '2+2 == 4', 'a = 3.2', 'b = 6-2.8', 'a == b', '50cm == 0.5m'],
	  seealso: ['unequal', 'smaller', 'larger', 'smallerEq', 'largerEq', 'compare', 'deepEqual', 'equalText']
	};
	return equal$1;
}

var equalText = {};

var hasRequiredEqualText;

function requireEqualText () {
	if (hasRequiredEqualText) return equalText;
	hasRequiredEqualText = 1;

	Object.defineProperty(equalText, "__esModule", {
	  value: true
	});
	equalText.equalTextDocs = void 0;
	equalText.equalTextDocs = {
	  name: 'equalText',
	  category: 'Relational',
	  syntax: ['equalText(x, y)'],
	  description: 'Check equality of two strings. Comparison is case sensitive. Returns true if the values are equal, and false if not.',
	  examples: ['equalText("Hello", "Hello")', 'equalText("a", "A")', 'equal("2e3", "2000")', 'equalText("2e3", "2000")', 'equalText("B", ["A", "B", "C"])'],
	  seealso: ['compare', 'compareNatural', 'compareText', 'equal']
	};
	return equalText;
}

var larger = {};

var hasRequiredLarger;

function requireLarger () {
	if (hasRequiredLarger) return larger;
	hasRequiredLarger = 1;

	Object.defineProperty(larger, "__esModule", {
	  value: true
	});
	larger.largerDocs = void 0;
	larger.largerDocs = {
	  name: 'larger',
	  category: 'Relational',
	  syntax: ['x > y', 'larger(x, y)'],
	  description: 'Check if value x is larger than y. Returns true if x is larger than y, and false if not. Comparing a value with NaN returns false.',
	  examples: ['2 > 3', '5 > 2*2', 'a = 3.3', 'b = 6-2.8', '(a > b)', '(b < a)', '5 cm > 2 inch'],
	  seealso: ['equal', 'unequal', 'smaller', 'smallerEq', 'largerEq', 'compare']
	};
	return larger;
}

var largerEq = {};

var hasRequiredLargerEq;

function requireLargerEq () {
	if (hasRequiredLargerEq) return largerEq;
	hasRequiredLargerEq = 1;

	Object.defineProperty(largerEq, "__esModule", {
	  value: true
	});
	largerEq.largerEqDocs = void 0;
	largerEq.largerEqDocs = {
	  name: 'largerEq',
	  category: 'Relational',
	  syntax: ['x >= y', 'largerEq(x, y)'],
	  description: 'Check if value x is larger or equal to y. Returns true if x is larger or equal to y, and false if not.',
	  examples: ['2 >= 1+1', '2 > 1+1', 'a = 3.2', 'b = 6-2.8', '(a >= b)'],
	  seealso: ['equal', 'unequal', 'smallerEq', 'smaller', 'compare']
	};
	return largerEq;
}

var smaller = {};

var hasRequiredSmaller;

function requireSmaller () {
	if (hasRequiredSmaller) return smaller;
	hasRequiredSmaller = 1;

	Object.defineProperty(smaller, "__esModule", {
	  value: true
	});
	smaller.smallerDocs = void 0;
	smaller.smallerDocs = {
	  name: 'smaller',
	  category: 'Relational',
	  syntax: ['x < y', 'smaller(x, y)'],
	  description: 'Check if value x is smaller than value y. Returns true if x is smaller than y, and false if not. Comparing a value with NaN returns false.',
	  examples: ['2 < 3', '5 < 2*2', 'a = 3.3', 'b = 6-2.8', '(a < b)', '5 cm < 2 inch'],
	  seealso: ['equal', 'unequal', 'larger', 'smallerEq', 'largerEq', 'compare']
	};
	return smaller;
}

var smallerEq = {};

var hasRequiredSmallerEq;

function requireSmallerEq () {
	if (hasRequiredSmallerEq) return smallerEq;
	hasRequiredSmallerEq = 1;

	Object.defineProperty(smallerEq, "__esModule", {
	  value: true
	});
	smallerEq.smallerEqDocs = void 0;
	smallerEq.smallerEqDocs = {
	  name: 'smallerEq',
	  category: 'Relational',
	  syntax: ['x <= y', 'smallerEq(x, y)'],
	  description: 'Check if value x is smaller or equal to value y. Returns true if x is smaller than y, and false if not.',
	  examples: ['2 <= 1+1', '2 < 1+1', 'a = 3.2', 'b = 6-2.8', '(a <= b)'],
	  seealso: ['equal', 'unequal', 'larger', 'smaller', 'largerEq', 'compare']
	};
	return smallerEq;
}

var unequal = {};

var hasRequiredUnequal;

function requireUnequal () {
	if (hasRequiredUnequal) return unequal;
	hasRequiredUnequal = 1;

	Object.defineProperty(unequal, "__esModule", {
	  value: true
	});
	unequal.unequalDocs = void 0;
	unequal.unequalDocs = {
	  name: 'unequal',
	  category: 'Relational',
	  syntax: ['x != y', 'unequal(x, y)'],
	  description: 'Check unequality of two values. Returns true if the values are unequal, and false if they are equal.',
	  examples: ['2+2 != 3', '2+2 != 4', 'a = 3.2', 'b = 6-2.8', 'a != b', '50cm != 0.5m', '5 cm != 2 inch'],
	  seealso: ['equal', 'smaller', 'larger', 'smallerEq', 'largerEq', 'compare', 'deepEqual']
	};
	return unequal;
}

var setCartesian = {};

var hasRequiredSetCartesian;

function requireSetCartesian () {
	if (hasRequiredSetCartesian) return setCartesian;
	hasRequiredSetCartesian = 1;

	Object.defineProperty(setCartesian, "__esModule", {
	  value: true
	});
	setCartesian.setCartesianDocs = void 0;
	setCartesian.setCartesianDocs = {
	  name: 'setCartesian',
	  category: 'Set',
	  syntax: ['setCartesian(set1, set2)'],
	  description: 'Create the cartesian product of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays and the values will be sorted in ascending order before the operation.',
	  examples: ['setCartesian([1, 2], [3, 4])'],
	  seealso: ['setUnion', 'setIntersect', 'setDifference', 'setPowerset']
	};
	return setCartesian;
}

var setDifference = {};

var hasRequiredSetDifference;

function requireSetDifference () {
	if (hasRequiredSetDifference) return setDifference;
	hasRequiredSetDifference = 1;

	Object.defineProperty(setDifference, "__esModule", {
	  value: true
	});
	setDifference.setDifferenceDocs = void 0;
	setDifference.setDifferenceDocs = {
	  name: 'setDifference',
	  category: 'Set',
	  syntax: ['setDifference(set1, set2)'],
	  description: 'Create the difference of two (multi)sets: every element of set1, that is not the element of set2. Multi-dimension arrays will be converted to single-dimension arrays before the operation.',
	  examples: ['setDifference([1, 2, 3, 4], [3, 4, 5, 6])', 'setDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]])'],
	  seealso: ['setUnion', 'setIntersect', 'setSymDifference']
	};
	return setDifference;
}

var setDistinct = {};

var hasRequiredSetDistinct;

function requireSetDistinct () {
	if (hasRequiredSetDistinct) return setDistinct;
	hasRequiredSetDistinct = 1;

	Object.defineProperty(setDistinct, "__esModule", {
	  value: true
	});
	setDistinct.setDistinctDocs = void 0;
	setDistinct.setDistinctDocs = {
	  name: 'setDistinct',
	  category: 'Set',
	  syntax: ['setDistinct(set)'],
	  description: 'Collect the distinct elements of a multiset. A multi-dimension array will be converted to a single-dimension array before the operation.',
	  examples: ['setDistinct([1, 1, 1, 2, 2, 3])'],
	  seealso: ['setMultiplicity']
	};
	return setDistinct;
}

var setIntersect = {};

var hasRequiredSetIntersect;

function requireSetIntersect () {
	if (hasRequiredSetIntersect) return setIntersect;
	hasRequiredSetIntersect = 1;

	Object.defineProperty(setIntersect, "__esModule", {
	  value: true
	});
	setIntersect.setIntersectDocs = void 0;
	setIntersect.setIntersectDocs = {
	  name: 'setIntersect',
	  category: 'Set',
	  syntax: ['setIntersect(set1, set2)'],
	  description: 'Create the intersection of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays before the operation.',
	  examples: ['setIntersect([1, 2, 3, 4], [3, 4, 5, 6])', 'setIntersect([[1, 2], [3, 4]], [[3, 4], [5, 6]])'],
	  seealso: ['setUnion', 'setDifference']
	};
	return setIntersect;
}

var setIsSubset = {};

var hasRequiredSetIsSubset;

function requireSetIsSubset () {
	if (hasRequiredSetIsSubset) return setIsSubset;
	hasRequiredSetIsSubset = 1;

	Object.defineProperty(setIsSubset, "__esModule", {
	  value: true
	});
	setIsSubset.setIsSubsetDocs = void 0;
	setIsSubset.setIsSubsetDocs = {
	  name: 'setIsSubset',
	  category: 'Set',
	  syntax: ['setIsSubset(set1, set2)'],
	  description: 'Check whether a (multi)set is a subset of another (multi)set: every element of set1 is the element of set2. Multi-dimension arrays will be converted to single-dimension arrays before the operation.',
	  examples: ['setIsSubset([1, 2], [3, 4, 5, 6])', 'setIsSubset([3, 4], [3, 4, 5, 6])'],
	  seealso: ['setUnion', 'setIntersect', 'setDifference']
	};
	return setIsSubset;
}

var setMultiplicity = {};

var hasRequiredSetMultiplicity;

function requireSetMultiplicity () {
	if (hasRequiredSetMultiplicity) return setMultiplicity;
	hasRequiredSetMultiplicity = 1;

	Object.defineProperty(setMultiplicity, "__esModule", {
	  value: true
	});
	setMultiplicity.setMultiplicityDocs = void 0;
	setMultiplicity.setMultiplicityDocs = {
	  name: 'setMultiplicity',
	  category: 'Set',
	  syntax: ['setMultiplicity(element, set)'],
	  description: 'Count the multiplicity of an element in a multiset. A multi-dimension array will be converted to a single-dimension array before the operation.',
	  examples: ['setMultiplicity(1, [1, 2, 2, 4])', 'setMultiplicity(2, [1, 2, 2, 4])'],
	  seealso: ['setDistinct', 'setSize']
	};
	return setMultiplicity;
}

var setPowerset = {};

var hasRequiredSetPowerset;

function requireSetPowerset () {
	if (hasRequiredSetPowerset) return setPowerset;
	hasRequiredSetPowerset = 1;

	Object.defineProperty(setPowerset, "__esModule", {
	  value: true
	});
	setPowerset.setPowersetDocs = void 0;
	setPowerset.setPowersetDocs = {
	  name: 'setPowerset',
	  category: 'Set',
	  syntax: ['setPowerset(set)'],
	  description: 'Create the powerset of a (multi)set: the powerset contains very possible subsets of a (multi)set. A multi-dimension array will be converted to a single-dimension array before the operation.',
	  examples: ['setPowerset([1, 2, 3])'],
	  seealso: ['setCartesian']
	};
	return setPowerset;
}

var setSize = {};

var hasRequiredSetSize;

function requireSetSize () {
	if (hasRequiredSetSize) return setSize;
	hasRequiredSetSize = 1;

	Object.defineProperty(setSize, "__esModule", {
	  value: true
	});
	setSize.setSizeDocs = void 0;
	setSize.setSizeDocs = {
	  name: 'setSize',
	  category: 'Set',
	  syntax: ['setSize(set)', 'setSize(set, unique)'],
	  description: 'Count the number of elements of a (multi)set. When the second parameter "unique" is true, count only the unique values. A multi-dimension array will be converted to a single-dimension array before the operation.',
	  examples: ['setSize([1, 2, 2, 4])', 'setSize([1, 2, 2, 4], true)'],
	  seealso: ['setUnion', 'setIntersect', 'setDifference']
	};
	return setSize;
}

var setSymDifference = {};

var hasRequiredSetSymDifference;

function requireSetSymDifference () {
	if (hasRequiredSetSymDifference) return setSymDifference;
	hasRequiredSetSymDifference = 1;

	Object.defineProperty(setSymDifference, "__esModule", {
	  value: true
	});
	setSymDifference.setSymDifferenceDocs = void 0;
	setSymDifference.setSymDifferenceDocs = {
	  name: 'setSymDifference',
	  category: 'Set',
	  syntax: ['setSymDifference(set1, set2)'],
	  description: 'Create the symmetric difference of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays before the operation.',
	  examples: ['setSymDifference([1, 2, 3, 4], [3, 4, 5, 6])', 'setSymDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]])'],
	  seealso: ['setUnion', 'setIntersect', 'setDifference']
	};
	return setSymDifference;
}

var setUnion = {};

var hasRequiredSetUnion;

function requireSetUnion () {
	if (hasRequiredSetUnion) return setUnion;
	hasRequiredSetUnion = 1;

	Object.defineProperty(setUnion, "__esModule", {
	  value: true
	});
	setUnion.setUnionDocs = void 0;
	setUnion.setUnionDocs = {
	  name: 'setUnion',
	  category: 'Set',
	  syntax: ['setUnion(set1, set2)'],
	  description: 'Create the union of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays before the operation.',
	  examples: ['setUnion([1, 2, 3, 4], [3, 4, 5, 6])', 'setUnion([[1, 2], [3, 4]], [[3, 4], [5, 6]])'],
	  seealso: ['setIntersect', 'setDifference']
	};
	return setUnion;
}

var zpk2tf$1 = {};

var hasRequiredZpk2tf$1;

function requireZpk2tf$1 () {
	if (hasRequiredZpk2tf$1) return zpk2tf$1;
	hasRequiredZpk2tf$1 = 1;

	Object.defineProperty(zpk2tf$1, "__esModule", {
	  value: true
	});
	zpk2tf$1.zpk2tfDocs = void 0;
	zpk2tf$1.zpk2tfDocs = {
	  name: 'zpk2tf',
	  category: 'Signal',
	  syntax: ['zpk2tf(z, p, k)'],
	  description: 'Compute the transfer function of a zero-pole-gain model.',
	  examples: ['zpk2tf([1, 2], [-1, -2], 1)', 'zpk2tf([1, 2], [-1, -2])', 'zpk2tf([1 - 3i, 2 + 2i], [-1, -2])'],
	  seealso: []
	};
	return zpk2tf$1;
}

var freqz$1 = {};

var hasRequiredFreqz$1;

function requireFreqz$1 () {
	if (hasRequiredFreqz$1) return freqz$1;
	hasRequiredFreqz$1 = 1;

	Object.defineProperty(freqz$1, "__esModule", {
	  value: true
	});
	freqz$1.freqzDocs = void 0;
	freqz$1.freqzDocs = {
	  name: 'freqz',
	  category: 'Signal',
	  syntax: ['freqz(b, a)', 'freqz(b, a, w)'],
	  description: 'Calculates the frequency response of a filter given its numerator and denominator coefficients.',
	  examples: ['freqz([1, 2], [1, 2, 3])', 'freqz([1, 2], [1, 2, 3], [0, 1])', 'freqz([1, 2], [1, 2, 3], 512)'],
	  seealso: []
	};
	return freqz$1;
}

var erf = {};

var hasRequiredErf;

function requireErf () {
	if (hasRequiredErf) return erf;
	hasRequiredErf = 1;

	Object.defineProperty(erf, "__esModule", {
	  value: true
	});
	erf.erfDocs = void 0;
	erf.erfDocs = {
	  name: 'erf',
	  category: 'Special',
	  syntax: ['erf(x)'],
	  description: 'Compute the erf function of a value using a rational Chebyshev approximations for different intervals of x',
	  examples: ['erf(0.2)', 'erf(-0.5)', 'erf(4)'],
	  seealso: []
	};
	return erf;
}

var zeta = {};

var hasRequiredZeta;

function requireZeta () {
	if (hasRequiredZeta) return zeta;
	hasRequiredZeta = 1;

	Object.defineProperty(zeta, "__esModule", {
	  value: true
	});
	zeta.zetaDocs = void 0;
	zeta.zetaDocs = {
	  name: 'zeta',
	  category: 'Special',
	  syntax: ['zeta(s)'],
	  description: 'Compute the Riemann Zeta Function using an infinite series and Riemann\'s Functional Equation for the entire complex plane',
	  examples: ['zeta(0.2)', 'zeta(-0.5)', 'zeta(4)'],
	  seealso: []
	};
	return zeta;
}

var mad$1 = {};

var hasRequiredMad$1;

function requireMad$1 () {
	if (hasRequiredMad$1) return mad$1;
	hasRequiredMad$1 = 1;

	Object.defineProperty(mad$1, "__esModule", {
	  value: true
	});
	mad$1.madDocs = void 0;
	mad$1.madDocs = {
	  name: 'mad',
	  category: 'Statistics',
	  syntax: ['mad(a, b, c, ...)', 'mad(A)'],
	  description: 'Compute the median absolute deviation of a matrix or a list with values. The median absolute deviation is defined as the median of the absolute deviations from the median.',
	  examples: ['mad(10, 20, 30)', 'mad([1, 2, 3])'],
	  seealso: ['mean', 'median', 'std', 'abs']
	};
	return mad$1;
}

var max$1 = {};

var hasRequiredMax;

function requireMax () {
	if (hasRequiredMax) return max$1;
	hasRequiredMax = 1;

	Object.defineProperty(max$1, "__esModule", {
	  value: true
	});
	max$1.maxDocs = void 0;
	max$1.maxDocs = {
	  name: 'max',
	  category: 'Statistics',
	  syntax: ['max(a, b, c, ...)', 'max(A)', 'max(A, dimension)'],
	  description: 'Compute the maximum value of a list of values. If any NaN values are found, the function yields the last NaN in the input.',
	  examples: ['max(2, 3, 4, 1)', 'max([2, 3, 4, 1])', 'max([2, 5; 4, 3])', 'max([2, 5; 4, 3], 1)', 'max([2, 5; 4, 3], 2)', 'max(2.7, 7.1, -4.5, 2.0, 4.1)', 'min(2.7, 7.1, -4.5, 2.0, 4.1)'],
	  seealso: ['mean', 'median', 'min', 'prod', 'std', 'sum', 'variance']
	};
	return max$1;
}

var mean$1 = {};

var hasRequiredMean$1;

function requireMean$1 () {
	if (hasRequiredMean$1) return mean$1;
	hasRequiredMean$1 = 1;

	Object.defineProperty(mean$1, "__esModule", {
	  value: true
	});
	mean$1.meanDocs = void 0;
	mean$1.meanDocs = {
	  name: 'mean',
	  category: 'Statistics',
	  syntax: ['mean(a, b, c, ...)', 'mean(A)', 'mean(A, dimension)'],
	  description: 'Compute the arithmetic mean of a list of values.',
	  examples: ['mean(2, 3, 4, 1)', 'mean([2, 3, 4, 1])', 'mean([2, 5; 4, 3])', 'mean([2, 5; 4, 3], 1)', 'mean([2, 5; 4, 3], 2)', 'mean([1.0, 2.7, 3.2, 4.0])'],
	  seealso: ['max', 'median', 'min', 'prod', 'std', 'sum', 'variance']
	};
	return mean$1;
}

var median$1 = {};

var hasRequiredMedian$1;

function requireMedian$1 () {
	if (hasRequiredMedian$1) return median$1;
	hasRequiredMedian$1 = 1;

	Object.defineProperty(median$1, "__esModule", {
	  value: true
	});
	median$1.medianDocs = void 0;
	median$1.medianDocs = {
	  name: 'median',
	  category: 'Statistics',
	  syntax: ['median(a, b, c, ...)', 'median(A)'],
	  description: 'Compute the median of all values. The values are sorted and the middle value is returned. In case of an even number of values, the average of the two middle values is returned.',
	  examples: ['median(5, 2, 7)', 'median([3, -1, 5, 7])'],
	  seealso: ['max', 'mean', 'min', 'prod', 'std', 'sum', 'variance', 'quantileSeq']
	};
	return median$1;
}

var min$1 = {};

var hasRequiredMin;

function requireMin () {
	if (hasRequiredMin) return min$1;
	hasRequiredMin = 1;

	Object.defineProperty(min$1, "__esModule", {
	  value: true
	});
	min$1.minDocs = void 0;
	min$1.minDocs = {
	  name: 'min',
	  category: 'Statistics',
	  syntax: ['min(a, b, c, ...)', 'min(A)', 'min(A, dimension)'],
	  description: 'Compute the minimum value of a list of values. If any NaN values are found, the function yields the last NaN in the input.',
	  examples: ['min(2, 3, 4, 1)', 'min([2, 3, 4, 1])', 'min([2, 5; 4, 3])', 'min([2, 5; 4, 3], 1)', 'min([2, 5; 4, 3], 2)', 'min(2.7, 7.1, -4.5, 2.0, 4.1)', 'max(2.7, 7.1, -4.5, 2.0, 4.1)'],
	  seealso: ['max', 'mean', 'median', 'prod', 'std', 'sum', 'variance']
	};
	return min$1;
}

var mode = {};

var hasRequiredMode;

function requireMode () {
	if (hasRequiredMode) return mode;
	hasRequiredMode = 1;

	Object.defineProperty(mode, "__esModule", {
	  value: true
	});
	mode.modeDocs = void 0;
	mode.modeDocs = {
	  name: 'mode',
	  category: 'Statistics',
	  syntax: ['mode(a, b, c, ...)', 'mode(A)', 'mode(A, a, b, B, c, ...)'],
	  description: 'Computes the mode of all values as an array. In case mode being more than one, multiple values are returned in an array.',
	  examples: ['mode(2, 1, 4, 3, 1)', 'mode([1, 2.7, 3.2, 4, 2.7])', 'mode(1, 4, 6, 1, 6)'],
	  seealso: ['max', 'mean', 'min', 'median', 'prod', 'std', 'sum', 'variance']
	};
	return mode;
}

var prod = {};

var hasRequiredProd;

function requireProd () {
	if (hasRequiredProd) return prod;
	hasRequiredProd = 1;

	Object.defineProperty(prod, "__esModule", {
	  value: true
	});
	prod.prodDocs = void 0;
	prod.prodDocs = {
	  name: 'prod',
	  category: 'Statistics',
	  syntax: ['prod(a, b, c, ...)', 'prod(A)'],
	  description: 'Compute the product of all values.',
	  examples: ['prod(2, 3, 4)', 'prod([2, 3, 4])', 'prod([2, 5; 4, 3])'],
	  seealso: ['max', 'mean', 'min', 'median', 'min', 'std', 'sum', 'variance']
	};
	return prod;
}

var quantileSeq$1 = {};

var hasRequiredQuantileSeq$1;

function requireQuantileSeq$1 () {
	if (hasRequiredQuantileSeq$1) return quantileSeq$1;
	hasRequiredQuantileSeq$1 = 1;

	Object.defineProperty(quantileSeq$1, "__esModule", {
	  value: true
	});
	quantileSeq$1.quantileSeqDocs = void 0;
	quantileSeq$1.quantileSeqDocs = {
	  name: 'quantileSeq',
	  category: 'Statistics',
	  syntax: ['quantileSeq(A, prob[, sorted])', 'quantileSeq(A, [prob1, prob2, ...][, sorted])', 'quantileSeq(A, N[, sorted])'],
	  description: 'Compute the prob order quantile of a matrix or a list with values. The sequence is sorted and the middle value is returned. Supported types of sequence values are: Number, BigNumber, Unit Supported types of probability are: Number, BigNumber. \n\nIn case of a (multi dimensional) array or matrix, the prob order quantile of all elements will be calculated.',
	  examples: ['quantileSeq([3, -1, 5, 7], 0.5)', 'quantileSeq([3, -1, 5, 7], [1/3, 2/3])', 'quantileSeq([3, -1, 5, 7], 2)', 'quantileSeq([-1, 3, 5, 7], 0.5, true)'],
	  seealso: ['mean', 'median', 'min', 'max', 'prod', 'std', 'sum', 'variance']
	};
	return quantileSeq$1;
}

var std$1 = {};

var hasRequiredStd$1;

function requireStd$1 () {
	if (hasRequiredStd$1) return std$1;
	hasRequiredStd$1 = 1;

	Object.defineProperty(std$1, "__esModule", {
	  value: true
	});
	std$1.stdDocs = void 0;
	std$1.stdDocs = {
	  name: 'std',
	  category: 'Statistics',
	  syntax: ['std(a, b, c, ...)', 'std(A)', 'std(A, dimension)', 'std(A, normalization)', 'std(A, dimension, normalization)'],
	  description: 'Compute the standard deviation of all values, defined as std(A) = sqrt(variance(A)). Optional parameter normalization can be "unbiased" (default), "uncorrected", or "biased".',
	  examples: ['std(2, 4, 6)', 'std([2, 4, 6, 8])', 'std([2, 4, 6, 8], "uncorrected")', 'std([2, 4, 6, 8], "biased")', 'std([1, 2, 3; 4, 5, 6])'],
	  seealso: ['max', 'mean', 'min', 'median', 'prod', 'sum', 'variance']
	};
	return std$1;
}

var cumsum$1 = {};

var hasRequiredCumsum$1;

function requireCumsum$1 () {
	if (hasRequiredCumsum$1) return cumsum$1;
	hasRequiredCumsum$1 = 1;

	Object.defineProperty(cumsum$1, "__esModule", {
	  value: true
	});
	cumsum$1.cumSumDocs = void 0;
	cumsum$1.cumSumDocs = {
	  name: 'cumsum',
	  category: 'Statistics',
	  syntax: ['cumsum(a, b, c, ...)', 'cumsum(A)'],
	  description: 'Compute the cumulative sum of all values.',
	  examples: ['cumsum(2, 3, 4, 1)', 'cumsum([2, 3, 4, 1])', 'cumsum([1, 2; 3, 4])', 'cumsum([1, 2; 3, 4], 1)', 'cumsum([1, 2; 3, 4], 2)'],
	  seealso: ['max', 'mean', 'median', 'min', 'prod', 'std', 'sum', 'variance']
	};
	return cumsum$1;
}

var sum$1 = {};

var hasRequiredSum$1;

function requireSum$1 () {
	if (hasRequiredSum$1) return sum$1;
	hasRequiredSum$1 = 1;

	Object.defineProperty(sum$1, "__esModule", {
	  value: true
	});
	sum$1.sumDocs = void 0;
	sum$1.sumDocs = {
	  name: 'sum',
	  category: 'Statistics',
	  syntax: ['sum(a, b, c, ...)', 'sum(A)', 'sum(A, dimension)'],
	  description: 'Compute the sum of all values.',
	  examples: ['sum(2, 3, 4, 1)', 'sum([2, 3, 4, 1])', 'sum([2, 5; 4, 3])'],
	  seealso: ['max', 'mean', 'median', 'min', 'prod', 'std', 'variance']
	};
	return sum$1;
}

var variance$1 = {};

var hasRequiredVariance$1;

function requireVariance$1 () {
	if (hasRequiredVariance$1) return variance$1;
	hasRequiredVariance$1 = 1;

	Object.defineProperty(variance$1, "__esModule", {
	  value: true
	});
	variance$1.varianceDocs = void 0;
	variance$1.varianceDocs = {
	  name: 'variance',
	  category: 'Statistics',
	  syntax: ['variance(a, b, c, ...)', 'variance(A)', 'variance(A, dimension)', 'variance(A, normalization)', 'variance(A, dimension, normalization)'],
	  description: 'Compute the variance of all values. Optional parameter normalization can be "unbiased" (default), "uncorrected", or "biased".',
	  examples: ['variance(2, 4, 6)', 'variance([2, 4, 6, 8])', 'variance([2, 4, 6, 8], "uncorrected")', 'variance([2, 4, 6, 8], "biased")', 'variance([1, 2, 3; 4, 5, 6])'],
	  seealso: ['max', 'mean', 'min', 'median', 'min', 'prod', 'std', 'sum']
	};
	return variance$1;
}

var corr$1 = {};

var hasRequiredCorr$1;

function requireCorr$1 () {
	if (hasRequiredCorr$1) return corr$1;
	hasRequiredCorr$1 = 1;

	Object.defineProperty(corr$1, "__esModule", {
	  value: true
	});
	corr$1.corrDocs = void 0;
	corr$1.corrDocs = {
	  name: 'corr',
	  category: 'Statistics',
	  syntax: ['corr(A,B)'],
	  description: 'Compute the correlation coefficient of a two list with values, For matrices, the matrix correlation coefficient is calculated.',
	  examples: ['corr([2, 4, 6, 8],[1, 2, 3, 6])', 'corr(matrix([[1, 2.2, 3, 4.8, 5], [1, 2, 3, 4, 5]]), matrix([[4, 5.3, 6.6, 7, 8], [1, 2, 3, 4, 5]]))'],
	  seealso: ['max', 'mean', 'min', 'median', 'min', 'prod', 'std', 'sum']
	};
	return corr$1;
}

var acos = {};

var hasRequiredAcos;

function requireAcos () {
	if (hasRequiredAcos) return acos;
	hasRequiredAcos = 1;

	Object.defineProperty(acos, "__esModule", {
	  value: true
	});
	acos.acosDocs = void 0;
	acos.acosDocs = {
	  name: 'acos',
	  category: 'Trigonometry',
	  syntax: ['acos(x)'],
	  description: 'Compute the inverse cosine of a value in radians.',
	  examples: ['acos(0.5)', 'acos(cos(2.3))'],
	  seealso: ['cos', 'atan', 'asin']
	};
	return acos;
}

var acosh = {};

var hasRequiredAcosh;

function requireAcosh () {
	if (hasRequiredAcosh) return acosh;
	hasRequiredAcosh = 1;

	Object.defineProperty(acosh, "__esModule", {
	  value: true
	});
	acosh.acoshDocs = void 0;
	acosh.acoshDocs = {
	  name: 'acosh',
	  category: 'Trigonometry',
	  syntax: ['acosh(x)'],
	  description: 'Calculate the hyperbolic arccos of a value, defined as `acosh(x) = ln(sqrt(x^2 - 1) + x)`.',
	  examples: ['acosh(1.5)'],
	  seealso: ['cosh', 'asinh', 'atanh']
	};
	return acosh;
}

var acot = {};

var hasRequiredAcot;

function requireAcot () {
	if (hasRequiredAcot) return acot;
	hasRequiredAcot = 1;

	Object.defineProperty(acot, "__esModule", {
	  value: true
	});
	acot.acotDocs = void 0;
	acot.acotDocs = {
	  name: 'acot',
	  category: 'Trigonometry',
	  syntax: ['acot(x)'],
	  description: 'Calculate the inverse cotangent of a value.',
	  examples: ['acot(0.5)', 'acot(cot(0.5))', 'acot(2)'],
	  seealso: ['cot', 'atan']
	};
	return acot;
}

var acoth = {};

var hasRequiredAcoth;

function requireAcoth () {
	if (hasRequiredAcoth) return acoth;
	hasRequiredAcoth = 1;

	Object.defineProperty(acoth, "__esModule", {
	  value: true
	});
	acoth.acothDocs = void 0;
	acoth.acothDocs = {
	  name: 'acoth',
	  category: 'Trigonometry',
	  syntax: ['acoth(x)'],
	  description: 'Calculate the inverse hyperbolic tangent of a value, defined as `acoth(x) = (ln((x+1)/x) + ln(x/(x-1))) / 2`.',
	  examples: ['acoth(2)', 'acoth(0.5)'],
	  seealso: ['acsch', 'asech']
	};
	return acoth;
}

var acsc = {};

var hasRequiredAcsc;

function requireAcsc () {
	if (hasRequiredAcsc) return acsc;
	hasRequiredAcsc = 1;

	Object.defineProperty(acsc, "__esModule", {
	  value: true
	});
	acsc.acscDocs = void 0;
	acsc.acscDocs = {
	  name: 'acsc',
	  category: 'Trigonometry',
	  syntax: ['acsc(x)'],
	  description: 'Calculate the inverse cotangent of a value.',
	  examples: ['acsc(2)', 'acsc(csc(0.5))', 'acsc(0.5)'],
	  seealso: ['csc', 'asin', 'asec']
	};
	return acsc;
}

var acsch = {};

var hasRequiredAcsch;

function requireAcsch () {
	if (hasRequiredAcsch) return acsch;
	hasRequiredAcsch = 1;

	Object.defineProperty(acsch, "__esModule", {
	  value: true
	});
	acsch.acschDocs = void 0;
	acsch.acschDocs = {
	  name: 'acsch',
	  category: 'Trigonometry',
	  syntax: ['acsch(x)'],
	  description: 'Calculate the inverse hyperbolic cosecant of a value, defined as `acsch(x) = ln(1/x + sqrt(1/x^2 + 1))`.',
	  examples: ['acsch(0.5)'],
	  seealso: ['asech', 'acoth']
	};
	return acsch;
}

var asec = {};

var hasRequiredAsec;

function requireAsec () {
	if (hasRequiredAsec) return asec;
	hasRequiredAsec = 1;

	Object.defineProperty(asec, "__esModule", {
	  value: true
	});
	asec.asecDocs = void 0;
	asec.asecDocs = {
	  name: 'asec',
	  category: 'Trigonometry',
	  syntax: ['asec(x)'],
	  description: 'Calculate the inverse secant of a value.',
	  examples: ['asec(0.5)', 'asec(sec(0.5))', 'asec(2)'],
	  seealso: ['acos', 'acot', 'acsc']
	};
	return asec;
}

var asech = {};

var hasRequiredAsech;

function requireAsech () {
	if (hasRequiredAsech) return asech;
	hasRequiredAsech = 1;

	Object.defineProperty(asech, "__esModule", {
	  value: true
	});
	asech.asechDocs = void 0;
	asech.asechDocs = {
	  name: 'asech',
	  category: 'Trigonometry',
	  syntax: ['asech(x)'],
	  description: 'Calculate the inverse secant of a value.',
	  examples: ['asech(0.5)'],
	  seealso: ['acsch', 'acoth']
	};
	return asech;
}

var asin = {};

var hasRequiredAsin;

function requireAsin () {
	if (hasRequiredAsin) return asin;
	hasRequiredAsin = 1;

	Object.defineProperty(asin, "__esModule", {
	  value: true
	});
	asin.asinDocs = void 0;
	asin.asinDocs = {
	  name: 'asin',
	  category: 'Trigonometry',
	  syntax: ['asin(x)'],
	  description: 'Compute the inverse sine of a value in radians.',
	  examples: ['asin(0.5)', 'asin(sin(0.5))'],
	  seealso: ['sin', 'acos', 'atan']
	};
	return asin;
}

var asinh = {};

var hasRequiredAsinh;

function requireAsinh () {
	if (hasRequiredAsinh) return asinh;
	hasRequiredAsinh = 1;

	Object.defineProperty(asinh, "__esModule", {
	  value: true
	});
	asinh.asinhDocs = void 0;
	asinh.asinhDocs = {
	  name: 'asinh',
	  category: 'Trigonometry',
	  syntax: ['asinh(x)'],
	  description: 'Calculate the hyperbolic arcsine of a value, defined as `asinh(x) = ln(x + sqrt(x^2 + 1))`.',
	  examples: ['asinh(0.5)'],
	  seealso: ['acosh', 'atanh']
	};
	return asinh;
}

var atan = {};

var hasRequiredAtan;

function requireAtan () {
	if (hasRequiredAtan) return atan;
	hasRequiredAtan = 1;

	Object.defineProperty(atan, "__esModule", {
	  value: true
	});
	atan.atanDocs = void 0;
	atan.atanDocs = {
	  name: 'atan',
	  category: 'Trigonometry',
	  syntax: ['atan(x)'],
	  description: 'Compute the inverse tangent of a value in radians.',
	  examples: ['atan(0.5)', 'atan(tan(0.5))'],
	  seealso: ['tan', 'acos', 'asin']
	};
	return atan;
}

var atan2 = {};

var hasRequiredAtan2;

function requireAtan2 () {
	if (hasRequiredAtan2) return atan2;
	hasRequiredAtan2 = 1;

	Object.defineProperty(atan2, "__esModule", {
	  value: true
	});
	atan2.atan2Docs = void 0;
	atan2.atan2Docs = {
	  name: 'atan2',
	  category: 'Trigonometry',
	  syntax: ['atan2(y, x)'],
	  description: 'Computes the principal value of the arc tangent of y/x in radians.',
	  examples: ['atan2(2, 2) / pi', 'angle = 60 deg in rad', 'x = cos(angle)', 'y = sin(angle)', 'atan2(y, x)'],
	  seealso: ['sin', 'cos', 'tan']
	};
	return atan2;
}

var atanh = {};

var hasRequiredAtanh;

function requireAtanh () {
	if (hasRequiredAtanh) return atanh;
	hasRequiredAtanh = 1;

	Object.defineProperty(atanh, "__esModule", {
	  value: true
	});
	atanh.atanhDocs = void 0;
	atanh.atanhDocs = {
	  name: 'atanh',
	  category: 'Trigonometry',
	  syntax: ['atanh(x)'],
	  description: 'Calculate the hyperbolic arctangent of a value, defined as `atanh(x) = ln((1 + x)/(1 - x)) / 2`.',
	  examples: ['atanh(0.5)'],
	  seealso: ['acosh', 'asinh']
	};
	return atanh;
}

var cos = {};

var hasRequiredCos;

function requireCos () {
	if (hasRequiredCos) return cos;
	hasRequiredCos = 1;

	Object.defineProperty(cos, "__esModule", {
	  value: true
	});
	cos.cosDocs = void 0;
	cos.cosDocs = {
	  name: 'cos',
	  category: 'Trigonometry',
	  syntax: ['cos(x)'],
	  description: 'Compute the cosine of x in radians.',
	  examples: ['cos(2)', 'cos(pi / 4) ^ 2', 'cos(180 deg)', 'cos(60 deg)', 'sin(0.2)^2 + cos(0.2)^2'],
	  seealso: ['acos', 'sin', 'tan']
	};
	return cos;
}

var cosh = {};

var hasRequiredCosh;

function requireCosh () {
	if (hasRequiredCosh) return cosh;
	hasRequiredCosh = 1;

	Object.defineProperty(cosh, "__esModule", {
	  value: true
	});
	cosh.coshDocs = void 0;
	cosh.coshDocs = {
	  name: 'cosh',
	  category: 'Trigonometry',
	  syntax: ['cosh(x)'],
	  description: 'Compute the hyperbolic cosine of x in radians.',
	  examples: ['cosh(0.5)'],
	  seealso: ['sinh', 'tanh', 'coth']
	};
	return cosh;
}

var cot = {};

var hasRequiredCot;

function requireCot () {
	if (hasRequiredCot) return cot;
	hasRequiredCot = 1;

	Object.defineProperty(cot, "__esModule", {
	  value: true
	});
	cot.cotDocs = void 0;
	cot.cotDocs = {
	  name: 'cot',
	  category: 'Trigonometry',
	  syntax: ['cot(x)'],
	  description: 'Compute the cotangent of x in radians. Defined as 1/tan(x)',
	  examples: ['cot(2)', '1 / tan(2)'],
	  seealso: ['sec', 'csc', 'tan']
	};
	return cot;
}

var coth = {};

var hasRequiredCoth;

function requireCoth () {
	if (hasRequiredCoth) return coth;
	hasRequiredCoth = 1;

	Object.defineProperty(coth, "__esModule", {
	  value: true
	});
	coth.cothDocs = void 0;
	coth.cothDocs = {
	  name: 'coth',
	  category: 'Trigonometry',
	  syntax: ['coth(x)'],
	  description: 'Compute the hyperbolic cotangent of x in radians.',
	  examples: ['coth(2)', '1 / tanh(2)'],
	  seealso: ['sech', 'csch', 'tanh']
	};
	return coth;
}

var csc = {};

var hasRequiredCsc;

function requireCsc () {
	if (hasRequiredCsc) return csc;
	hasRequiredCsc = 1;

	Object.defineProperty(csc, "__esModule", {
	  value: true
	});
	csc.cscDocs = void 0;
	csc.cscDocs = {
	  name: 'csc',
	  category: 'Trigonometry',
	  syntax: ['csc(x)'],
	  description: 'Compute the cosecant of x in radians. Defined as 1/sin(x)',
	  examples: ['csc(2)', '1 / sin(2)'],
	  seealso: ['sec', 'cot', 'sin']
	};
	return csc;
}

var csch = {};

var hasRequiredCsch;

function requireCsch () {
	if (hasRequiredCsch) return csch;
	hasRequiredCsch = 1;

	Object.defineProperty(csch, "__esModule", {
	  value: true
	});
	csch.cschDocs = void 0;
	csch.cschDocs = {
	  name: 'csch',
	  category: 'Trigonometry',
	  syntax: ['csch(x)'],
	  description: 'Compute the hyperbolic cosecant of x in radians. Defined as 1/sinh(x)',
	  examples: ['csch(2)', '1 / sinh(2)'],
	  seealso: ['sech', 'coth', 'sinh']
	};
	return csch;
}

var sec = {};

var hasRequiredSec;

function requireSec () {
	if (hasRequiredSec) return sec;
	hasRequiredSec = 1;

	Object.defineProperty(sec, "__esModule", {
	  value: true
	});
	sec.secDocs = void 0;
	sec.secDocs = {
	  name: 'sec',
	  category: 'Trigonometry',
	  syntax: ['sec(x)'],
	  description: 'Compute the secant of x in radians. Defined as 1/cos(x)',
	  examples: ['sec(2)', '1 / cos(2)'],
	  seealso: ['cot', 'csc', 'cos']
	};
	return sec;
}

var sech = {};

var hasRequiredSech;

function requireSech () {
	if (hasRequiredSech) return sech;
	hasRequiredSech = 1;

	Object.defineProperty(sech, "__esModule", {
	  value: true
	});
	sech.sechDocs = void 0;
	sech.sechDocs = {
	  name: 'sech',
	  category: 'Trigonometry',
	  syntax: ['sech(x)'],
	  description: 'Compute the hyperbolic secant of x in radians. Defined as 1/cosh(x)',
	  examples: ['sech(2)', '1 / cosh(2)'],
	  seealso: ['coth', 'csch', 'cosh']
	};
	return sech;
}

var sin = {};

var hasRequiredSin;

function requireSin () {
	if (hasRequiredSin) return sin;
	hasRequiredSin = 1;

	Object.defineProperty(sin, "__esModule", {
	  value: true
	});
	sin.sinDocs = void 0;
	sin.sinDocs = {
	  name: 'sin',
	  category: 'Trigonometry',
	  syntax: ['sin(x)'],
	  description: 'Compute the sine of x in radians.',
	  examples: ['sin(2)', 'sin(pi / 4) ^ 2', 'sin(90 deg)', 'sin(30 deg)', 'sin(0.2)^2 + cos(0.2)^2'],
	  seealso: ['asin', 'cos', 'tan']
	};
	return sin;
}

var sinh = {};

var hasRequiredSinh;

function requireSinh () {
	if (hasRequiredSinh) return sinh;
	hasRequiredSinh = 1;

	Object.defineProperty(sinh, "__esModule", {
	  value: true
	});
	sinh.sinhDocs = void 0;
	sinh.sinhDocs = {
	  name: 'sinh',
	  category: 'Trigonometry',
	  syntax: ['sinh(x)'],
	  description: 'Compute the hyperbolic sine of x in radians.',
	  examples: ['sinh(0.5)'],
	  seealso: ['cosh', 'tanh']
	};
	return sinh;
}

var tan = {};

var hasRequiredTan;

function requireTan () {
	if (hasRequiredTan) return tan;
	hasRequiredTan = 1;

	Object.defineProperty(tan, "__esModule", {
	  value: true
	});
	tan.tanDocs = void 0;
	tan.tanDocs = {
	  name: 'tan',
	  category: 'Trigonometry',
	  syntax: ['tan(x)'],
	  description: 'Compute the tangent of x in radians.',
	  examples: ['tan(0.5)', 'sin(0.5) / cos(0.5)', 'tan(pi / 4)', 'tan(45 deg)'],
	  seealso: ['atan', 'sin', 'cos']
	};
	return tan;
}

var tanh = {};

var hasRequiredTanh;

function requireTanh () {
	if (hasRequiredTanh) return tanh;
	hasRequiredTanh = 1;

	Object.defineProperty(tanh, "__esModule", {
	  value: true
	});
	tanh.tanhDocs = void 0;
	tanh.tanhDocs = {
	  name: 'tanh',
	  category: 'Trigonometry',
	  syntax: ['tanh(x)'],
	  description: 'Compute the hyperbolic tangent of x in radians.',
	  examples: ['tanh(0.5)', 'sinh(0.5) / cosh(0.5)'],
	  seealso: ['sinh', 'cosh']
	};
	return tanh;
}

var to = {};

var hasRequiredTo;

function requireTo () {
	if (hasRequiredTo) return to;
	hasRequiredTo = 1;

	Object.defineProperty(to, "__esModule", {
	  value: true
	});
	to.toDocs = void 0;
	to.toDocs = {
	  name: 'to',
	  category: 'Units',
	  syntax: ['x to unit', 'to(x, unit)'],
	  description: 'Change the unit of a value.',
	  examples: ['5 inch to cm', '3.2kg to g', '16 bytes in bits'],
	  seealso: []
	};
	return to;
}

var toBest = {};

var hasRequiredToBest;

function requireToBest () {
	if (hasRequiredToBest) return toBest;
	hasRequiredToBest = 1;

	Object.defineProperty(toBest, "__esModule", {
	  value: true
	});
	toBest.toBestDocs = void 0;
	toBest.toBestDocs = {
	  name: 'toBest',
	  category: 'Units',
	  syntax: ['toBest(x)', 'toBest(x, unitList)', 'toBest(x, unitList, options)'],
	  description: 'Converts to the most appropriate display unit.',
	  examples: ['toBest(unit(5000, "m"))', 'toBest(unit(3500000, "W"))', 'toBest(unit(0.000000123, "A"))', 'toBest(unit(10, "m"), "cm")', 'toBest(unit(10, "m"), "mm,km", {offset: 1.5})'],
	  seealso: []
	};
	return toBest;
}

var bin = {};

var hasRequiredBin;

function requireBin () {
	if (hasRequiredBin) return bin;
	hasRequiredBin = 1;

	Object.defineProperty(bin, "__esModule", {
	  value: true
	});
	bin.binDocs = void 0;
	bin.binDocs = {
	  name: 'bin',
	  category: 'Utils',
	  syntax: ['bin(value)'],
	  description: 'Format a number as binary',
	  examples: ['bin(2)'],
	  seealso: ['oct', 'hex']
	};
	return bin;
}

var clone = {};

var hasRequiredClone;

function requireClone () {
	if (hasRequiredClone) return clone;
	hasRequiredClone = 1;

	Object.defineProperty(clone, "__esModule", {
	  value: true
	});
	clone.cloneDocs = void 0;
	clone.cloneDocs = {
	  name: 'clone',
	  category: 'Utils',
	  syntax: ['clone(x)'],
	  description: 'Clone a variable. Creates a copy of primitive variables, and a deep copy of matrices',
	  examples: ['clone(3.5)', 'clone(2 - 4i)', 'clone(45 deg)', 'clone([1, 2; 3, 4])', 'clone("hello world")'],
	  seealso: []
	};
	return clone;
}

var format$1 = {};

var hasRequiredFormat;

function requireFormat () {
	if (hasRequiredFormat) return format$1;
	hasRequiredFormat = 1;

	Object.defineProperty(format$1, "__esModule", {
	  value: true
	});
	format$1.formatDocs = void 0;
	format$1.formatDocs = {
	  name: 'format',
	  category: 'Utils',
	  syntax: ['format(value)', 'format(value, precision)'],
	  description: 'Format a value of any type as string.',
	  examples: ['format(2.3)', 'format(3 - 4i)', 'format([])', 'format(pi, 3)'],
	  seealso: ['print']
	};
	return format$1;
}

var hasNumericValue = {};

var hasRequiredHasNumericValue;

function requireHasNumericValue () {
	if (hasRequiredHasNumericValue) return hasNumericValue;
	hasRequiredHasNumericValue = 1;

	Object.defineProperty(hasNumericValue, "__esModule", {
	  value: true
	});
	hasNumericValue.hasNumericValueDocs = void 0;
	hasNumericValue.hasNumericValueDocs = {
	  name: 'hasNumericValue',
	  category: 'Utils',
	  syntax: ['hasNumericValue(x)'],
	  description: 'Test whether a value is an numeric value. ' + 'In case of a string, true is returned if the string contains a numeric value.',
	  examples: ['hasNumericValue(2)', 'hasNumericValue("2")', 'isNumeric("2")', 'hasNumericValue(0)', 'hasNumericValue(bignumber(500))', 'hasNumericValue(fraction(0.125))', 'hasNumericValue(2 + 3i)', 'hasNumericValue([2.3, "foo", false])'],
	  seealso: ['isInteger', 'isZero', 'isNegative', 'isPositive', 'isNaN', 'isNumeric']
	};
	return hasNumericValue;
}

var hex = {};

var hasRequiredHex;

function requireHex () {
	if (hasRequiredHex) return hex;
	hasRequiredHex = 1;

	Object.defineProperty(hex, "__esModule", {
	  value: true
	});
	hex.hexDocs = void 0;
	hex.hexDocs = {
	  name: 'hex',
	  category: 'Utils',
	  syntax: ['hex(value)'],
	  description: 'Format a number as hexadecimal',
	  examples: ['hex(240)'],
	  seealso: ['bin', 'oct']
	};
	return hex;
}

var isInteger = {};

var hasRequiredIsInteger;

function requireIsInteger () {
	if (hasRequiredIsInteger) return isInteger;
	hasRequiredIsInteger = 1;

	Object.defineProperty(isInteger, "__esModule", {
	  value: true
	});
	isInteger.isIntegerDocs = void 0;
	isInteger.isIntegerDocs = {
	  name: 'isInteger',
	  category: 'Utils',
	  syntax: ['isInteger(x)'],
	  description: 'Test whether a value is an integer number.',
	  examples: ['isInteger(2)', 'isInteger(3.5)', 'isInteger([3, 0.5, -2])'],
	  seealso: ['isNegative', 'isNumeric', 'isPositive', 'isZero']
	};
	return isInteger;
}

var _isNaN = {};

var hasRequired_isNaN;

function require_isNaN () {
	if (hasRequired_isNaN) return _isNaN;
	hasRequired_isNaN = 1;

	Object.defineProperty(_isNaN, "__esModule", {
	  value: true
	});
	_isNaN.isNaNDocs = void 0;
	_isNaN.isNaNDocs = {
	  name: 'isNaN',
	  category: 'Utils',
	  syntax: ['isNaN(x)'],
	  description: 'Test whether a value is NaN (not a number)',
	  examples: ['isNaN(2)', 'isNaN(0 / 0)', 'isNaN(NaN)', 'isNaN(Infinity)'],
	  seealso: ['isNegative', 'isNumeric', 'isPositive', 'isZero', 'isFinite', 'isBounded']
	};
	return _isNaN;
}

var isBounded = {};

var hasRequiredIsBounded;

function requireIsBounded () {
	if (hasRequiredIsBounded) return isBounded;
	hasRequiredIsBounded = 1;

	Object.defineProperty(isBounded, "__esModule", {
	  value: true
	});
	isBounded.isBoundedDocs = void 0;
	isBounded.isBoundedDocs = {
	  name: 'isBounded',
	  category: 'Utils',
	  syntax: ['isBounded(x)'],
	  description: 'Test whether a value or its entries are bounded.',
	  examples: ['isBounded(Infinity)', 'isBounded(bigint(3))', 'isBounded([3, -Infinity, -3])'],
	  seealso: ['isFinite', 'isNumeric', 'isNaN', 'isNegative', 'isPositive']
	};
	return isBounded;
}

var _isFinite = {};

var hasRequired_isFinite;

function require_isFinite () {
	if (hasRequired_isFinite) return _isFinite;
	hasRequired_isFinite = 1;

	Object.defineProperty(_isFinite, "__esModule", {
	  value: true
	});
	_isFinite.isFiniteDocs = void 0;
	_isFinite.isFiniteDocs = {
	  name: 'isFinite',
	  category: 'Utils',
	  syntax: ['isFinite(x)'],
	  description: 'Test whether a value is finite, elementwise on collections.',
	  examples: ['isFinite(Infinity)', 'isFinite(bigint(3))', 'isFinite([3, -Infinity, -3])'],
	  seealso: ['isBounded', 'isNumeric', 'isNaN', 'isNegative', 'isPositive']
	};
	return _isFinite;
}

var isNegative = {};

var hasRequiredIsNegative;

function requireIsNegative () {
	if (hasRequiredIsNegative) return isNegative;
	hasRequiredIsNegative = 1;

	Object.defineProperty(isNegative, "__esModule", {
	  value: true
	});
	isNegative.isNegativeDocs = void 0;
	isNegative.isNegativeDocs = {
	  name: 'isNegative',
	  category: 'Utils',
	  syntax: ['isNegative(x)'],
	  description: 'Test whether a value is negative: smaller than zero.',
	  examples: ['isNegative(2)', 'isNegative(0)', 'isNegative(-4)', 'isNegative([3, 0.5, -2])'],
	  seealso: ['isInteger', 'isNumeric', 'isPositive', 'isZero']
	};
	return isNegative;
}

var isNumeric = {};

var hasRequiredIsNumeric;

function requireIsNumeric () {
	if (hasRequiredIsNumeric) return isNumeric;
	hasRequiredIsNumeric = 1;

	Object.defineProperty(isNumeric, "__esModule", {
	  value: true
	});
	isNumeric.isNumericDocs = void 0;
	isNumeric.isNumericDocs = {
	  name: 'isNumeric',
	  category: 'Utils',
	  syntax: ['isNumeric(x)'],
	  description: 'Test whether a value is a numeric value. ' + 'Returns true when the input is a number, BigNumber, Fraction, or boolean.',
	  examples: ['isNumeric(2)', 'isNumeric("2")', 'hasNumericValue("2")', 'isNumeric(0)', 'isNumeric(bignumber(500))', 'isNumeric(fraction(0.125))', 'isNumeric(2 + 3i)', 'isNumeric([2.3, "foo", false])'],
	  seealso: ['isInteger', 'isZero', 'isNegative', 'isPositive', 'isNaN', 'hasNumericValue', 'isFinite', 'isBounded']
	};
	return isNumeric;
}

var isPositive = {};

var hasRequiredIsPositive;

function requireIsPositive () {
	if (hasRequiredIsPositive) return isPositive;
	hasRequiredIsPositive = 1;

	Object.defineProperty(isPositive, "__esModule", {
	  value: true
	});
	isPositive.isPositiveDocs = void 0;
	isPositive.isPositiveDocs = {
	  name: 'isPositive',
	  category: 'Utils',
	  syntax: ['isPositive(x)'],
	  description: 'Test whether a value is positive: larger than zero.',
	  examples: ['isPositive(2)', 'isPositive(0)', 'isPositive(-4)', 'isPositive([3, 0.5, -2])'],
	  seealso: ['isInteger', 'isNumeric', 'isNegative', 'isZero']
	};
	return isPositive;
}

var isPrime = {};

var hasRequiredIsPrime;

function requireIsPrime () {
	if (hasRequiredIsPrime) return isPrime;
	hasRequiredIsPrime = 1;

	Object.defineProperty(isPrime, "__esModule", {
	  value: true
	});
	isPrime.isPrimeDocs = void 0;
	isPrime.isPrimeDocs = {
	  name: 'isPrime',
	  category: 'Utils',
	  syntax: ['isPrime(x)'],
	  description: 'Test whether a value is prime: has no divisors other than itself and one.',
	  examples: ['isPrime(3)', 'isPrime(-2)', 'isPrime([2, 17, 100])'],
	  seealso: ['isInteger', 'isNumeric', 'isNegative', 'isZero']
	};
	return isPrime;
}

var isZero = {};

var hasRequiredIsZero;

function requireIsZero () {
	if (hasRequiredIsZero) return isZero;
	hasRequiredIsZero = 1;

	Object.defineProperty(isZero, "__esModule", {
	  value: true
	});
	isZero.isZeroDocs = void 0;
	isZero.isZeroDocs = {
	  name: 'isZero',
	  category: 'Utils',
	  syntax: ['isZero(x)'],
	  description: 'Test whether a value is zero.',
	  examples: ['isZero(2)', 'isZero(0)', 'isZero(-4)', 'isZero([3, 0, -2, 0])'],
	  seealso: ['isInteger', 'isNumeric', 'isNegative', 'isPositive']
	};
	return isZero;
}

var numeric = {};

var hasRequiredNumeric;

function requireNumeric () {
	if (hasRequiredNumeric) return numeric;
	hasRequiredNumeric = 1;

	Object.defineProperty(numeric, "__esModule", {
	  value: true
	});
	numeric.numericDocs = void 0;
	numeric.numericDocs = {
	  name: 'numeric',
	  category: 'Utils',
	  syntax: ['numeric(x)'],
	  description: 'Convert a numeric input to a specific numeric type: number, BigNumber, bigint, or Fraction.',
	  examples: ['numeric("4")', 'numeric("4", "number")', 'numeric("4", "bigint")', 'numeric("4", "BigNumber")', 'numeric("4", "Fraction")', 'numeric(4, "Fraction")', 'numeric(fraction(2, 5), "number")'],
	  seealso: ['number', 'bigint', 'fraction', 'bignumber', 'string', 'format']
	};
	return numeric;
}

var oct = {};

var hasRequiredOct;

function requireOct () {
	if (hasRequiredOct) return oct;
	hasRequiredOct = 1;

	Object.defineProperty(oct, "__esModule", {
	  value: true
	});
	oct.octDocs = void 0;
	oct.octDocs = {
	  name: 'oct',
	  category: 'Utils',
	  syntax: ['oct(value)'],
	  description: 'Format a number as octal',
	  examples: ['oct(56)'],
	  seealso: ['bin', 'hex']
	};
	return oct;
}

var print = {};

var hasRequiredPrint;

function requirePrint () {
	if (hasRequiredPrint) return print;
	hasRequiredPrint = 1;

	Object.defineProperty(print, "__esModule", {
	  value: true
	});
	print.printDocs = void 0;
	print.printDocs = {
	  name: 'print',
	  category: 'Utils',
	  syntax: ['print(template, values)', 'print(template, values, precision)'],
	  description: 'Interpolate values into a string template.',
	  examples: ['print("Lucy is $age years old", {age: 5})', 'print("The value of pi is $pi", {pi: pi}, 3)', 'print("Hello, $user.name!", {user: {name: "John"}})', 'print("Values: $1, $2, $3", [6, 9, 4])'],
	  seealso: ['format']
	};
	return print;
}

var typeOf = {};

var hasRequiredTypeOf;

function requireTypeOf () {
	if (hasRequiredTypeOf) return typeOf;
	hasRequiredTypeOf = 1;

	Object.defineProperty(typeOf, "__esModule", {
	  value: true
	});
	typeOf.typeOfDocs = void 0;
	typeOf.typeOfDocs = {
	  name: 'typeOf',
	  category: 'Utils',
	  syntax: ['typeOf(x)'],
	  description: 'Get the type of a variable.',
	  examples: ['typeOf(3.5)', 'typeOf(2 - 4i)', 'typeOf(45 deg)', 'typeOf("hello world")'],
	  seealso: ['getMatrixDataType']
	};
	return typeOf;
}

var solveODE = {};

var hasRequiredSolveODE;

function requireSolveODE () {
	if (hasRequiredSolveODE) return solveODE;
	hasRequiredSolveODE = 1;

	Object.defineProperty(solveODE, "__esModule", {
	  value: true
	});
	solveODE.solveODEDocs = void 0;
	solveODE.solveODEDocs = {
	  name: 'solveODE',
	  category: 'Numeric',
	  syntax: ['solveODE(func, tspan, y0)', 'solveODE(func, tspan, y0, options)'],
	  description: 'Numerical Integration of Ordinary Differential Equations.',
	  examples: ['f(t,y) = y', 'tspan = [0, 4]', 'solveODE(f, tspan, 1)', 'solveODE(f, tspan, [1, 2])', 'solveODE(f, tspan, 1, { method:"RK23", maxStep:0.1 })'],
	  seealso: ['derivative', 'simplifyCore']
	};
	return solveODE;
}

var hasRequiredEmbeddedDocs;

function requireEmbeddedDocs () {
	if (hasRequiredEmbeddedDocs) return embeddedDocs;
	hasRequiredEmbeddedDocs = 1;

	Object.defineProperty(embeddedDocs, "__esModule", {
	  value: true
	});
	embeddedDocs.embeddedDocs = void 0;
	var _e = requireE();
	var _false = require_false();
	var _i = requireI();
	var _Infinity = require_Infinity();
	var _LN = requireLN10();
	var _LN2 = requireLN2();
	var _LOG10E = requireLOG10E();
	var _LOG2E = requireLOG2E();
	var _NaN = require_NaN();
	var _null = require_null();
	var _phi = requirePhi();
	var _pi = requirePi();
	var _SQRT1_ = requireSQRT1_2();
	var _SQRT = requireSQRT2();
	var _tau = requireTau();
	var _true = require_true();
	var _version = requireVersion$1();
	var _bignumber = requireBignumber();
	var _bigint = requireBigint();
	var _boolean = requireBoolean();
	var _complex = requireComplex();
	var _createUnit = requireCreateUnit();
	var _fraction = requireFraction();
	var _index = requireConstruction();
	var _matrix = requireMatrix();
	var _number = requireNumber();
	var _sparse = requireSparse();
	var _splitUnit = requireSplitUnit();
	var _string = requireString();
	var _unit = requireUnit();
	var _config = requireConfig();
	var _import = require_import$1();
	var _typed = requireTyped();
	var _derivative = requireDerivative$1();
	var _leafCount = requireLeafCount$1();
	var _lsolve = requireLsolve();
	var _lsolveAll = requireLsolveAll();
	var _lup = requireLup();
	var _lusolve = requireLusolve();
	var _polynomialRoot = requirePolynomialRoot();
	var _qr = requireQr();
	var _rationalize = requireRationalize$1();
	var _resolve = requireResolve$1();
	var _simplify = requireSimplify$1();
	var _simplifyConstant = requireSimplifyConstant$1();
	var _simplifyCore = requireSimplifyCore$1();
	var _slu = requireSlu();
	var _symbolicEqual = requireSymbolicEqual$1();
	var _usolve = requireUsolve();
	var _usolveAll = requireUsolveAll();
	var _abs = requireAbs();
	var _add = requireAdd();
	var _cbrt = requireCbrt();
	var _ceil = requireCeil();
	var _cube = requireCube();
	var _divide = requireDivide$1();
	var _dotDivide = requireDotDivide();
	var _dotMultiply = requireDotMultiply();
	var _dotPow = requireDotPow();
	var _exp = requireExp();
	var _expm = requireExpm$1();
	var _expm2 = requireExpm1();
	var _fix = requireFix();
	var _floor = requireFloor();
	var _gcd = requireGcd();
	var _hypot = requireHypot();
	var _invmod = requireInvmod();
	var _lcm = requireLcm();
	var _log = requireLog();
	var _log2 = requireLog10();
	var _log1p = requireLog1p();
	var _log3 = requireLog2();
	var _mod = requireMod();
	var _multiply = requireMultiply();
	var _norm = requireNorm();
	var _nthRoot = requireNthRoot();
	var _nthRoots = requireNthRoots();
	var _pow = requirePow();
	var _round = requireRound();
	var _sign = requireSign();
	var _sqrt = requireSqrt();
	var _sqrtm = requireSqrtm$1();
	var _sylvester = requireSylvester$1();
	var _schur = requireSchur$1();
	var _lyap = requireLyap$1();
	var _square = requireSquare();
	var _subtract = requireSubtract();
	var _unaryMinus = requireUnaryMinus();
	var _unaryPlus = requireUnaryPlus();
	var _xgcd = requireXgcd();
	var _num = requireNum$1();
	var _den = requireDen$1();
	var _bitAnd = requireBitAnd();
	var _bitNot = requireBitNot();
	var _bitOr = requireBitOr();
	var _bitXor = requireBitXor();
	var _leftShift = requireLeftShift();
	var _rightArithShift = requireRightArithShift();
	var _rightLogShift = requireRightLogShift();
	var _bellNumbers = requireBellNumbers$1();
	var _catalan = requireCatalan$1();
	var _composition = requireComposition$1();
	var _stirlingS = requireStirlingS2$1();
	var _arg = requireArg();
	var _conj = requireConj();
	var _im = requireIm();
	var _re = requireRe();
	var _evaluate = requireEvaluate();
	var _parser = requireParser$1();
	var _parse = requireParse$1();
	var _compile = requireCompile$1();
	var _help = requireHelp$1();
	var _distance = requireDistance$1();
	var _intersect = requireIntersect$1();
	var _and = requireAnd();
	var _not = requireNot();
	var _nullish = requireNullish();
	var _or = requireOr();
	var _xor = requireXor();
	var _mapSlices = requireMapSlices();
	var _column = requireColumn();
	var _concat = requireConcat();
	var _count = requireCount();
	var _cross = requireCross();
	var _ctranspose = requireCtranspose();
	var _det = requireDet$1();
	var _diag = requireDiag();
	var _diff = requireDiff();
	var _dot = requireDot();
	var _eigs = requireEigs$1();
	var _filter = requireFilter();
	var _flatten = requireFlatten();
	var _forEach = requireForEach();
	var _getMatrixDataType = requireGetMatrixDataType();
	var _identity = requireIdentity();
	var _inv = requireInv$1();
	var _pinv = requirePinv$1();
	var _kron = requireKron();
	var _map = requireMap();
	var _matrixFromColumns = requireMatrixFromColumns();
	var _matrixFromFunction = requireMatrixFromFunction();
	var _matrixFromRows = requireMatrixFromRows();
	var _ones = requireOnes();
	var _partitionSelect = requirePartitionSelect();
	var _range = requireRange();
	var _reshape = requireReshape();
	var _resize = requireResize();
	var _rotate = requireRotate();
	var _rotationMatrix = requireRotationMatrix();
	var _row = requireRow();
	var _size = requireSize();
	var _sort = requireSort();
	var _squeeze = requireSqueeze();
	var _subset = requireSubset();
	var _trace = requireTrace();
	var _transpose = requireTranspose();
	var _zeros = requireZeros();
	var _fft = requireFft();
	var _ifft = requireIfft();
	var _bernoulli = requireBernoulli$1();
	var _combinations = requireCombinations$1();
	var _combinationsWithRep = requireCombinationsWithRep$1();
	var _factorial = requireFactorial$1();
	var _gamma = requireGamma$1();
	var _lgamma = requireLgamma$1();
	var _kldivergence = requireKldivergence$1();
	var _multinomial = requireMultinomial$1();
	var _permutations = requirePermutations$1();
	var _pickRandom = requirePickRandom$1();
	var _random = requireRandom$1();
	var _randomInt = requireRandomInt$1();
	var _compare = requireCompare();
	var _compareNatural = requireCompareNatural();
	var _compareText = requireCompareText();
	var _deepEqual = requireDeepEqual();
	var _equal = requireEqual();
	var _equalText = requireEqualText();
	var _larger = requireLarger();
	var _largerEq = requireLargerEq();
	var _smaller = requireSmaller();
	var _smallerEq = requireSmallerEq();
	var _unequal = requireUnequal();
	var _setCartesian = requireSetCartesian();
	var _setDifference = requireSetDifference();
	var _setDistinct = requireSetDistinct();
	var _setIntersect = requireSetIntersect();
	var _setIsSubset = requireSetIsSubset();
	var _setMultiplicity = requireSetMultiplicity();
	var _setPowerset = requireSetPowerset();
	var _setSize = requireSetSize();
	var _setSymDifference = requireSetSymDifference();
	var _setUnion = requireSetUnion();
	var _zpk2tf = requireZpk2tf$1();
	var _freqz = requireFreqz$1();
	var _erf = requireErf();
	var _zeta = requireZeta();
	var _mad = requireMad$1();
	var _max = requireMax();
	var _mean = requireMean$1();
	var _median = requireMedian$1();
	var _min = requireMin();
	var _mode = requireMode();
	var _prod = requireProd();
	var _quantileSeq = requireQuantileSeq$1();
	var _std = requireStd$1();
	var _cumsum = requireCumsum$1();
	var _sum = requireSum$1();
	var _variance = requireVariance$1();
	var _corr = requireCorr$1();
	var _acos = requireAcos();
	var _acosh = requireAcosh();
	var _acot = requireAcot();
	var _acoth = requireAcoth();
	var _acsc = requireAcsc();
	var _acsch = requireAcsch();
	var _asec = requireAsec();
	var _asech = requireAsech();
	var _asin = requireAsin();
	var _asinh = requireAsinh();
	var _atan = requireAtan();
	var _atan2 = requireAtan2();
	var _atanh = requireAtanh();
	var _cos = requireCos();
	var _cosh = requireCosh();
	var _cot = requireCot();
	var _coth = requireCoth();
	var _csc = requireCsc();
	var _csch = requireCsch();
	var _sec = requireSec();
	var _sech = requireSech();
	var _sin = requireSin();
	var _sinh = requireSinh();
	var _tan = requireTan();
	var _tanh = requireTanh();
	var _to = requireTo();
	var _toBest = requireToBest();
	var _bin = requireBin();
	var _clone = requireClone();
	var _format = requireFormat();
	var _hasNumericValue = requireHasNumericValue();
	var _hex = requireHex();
	var _isInteger = requireIsInteger();
	var _isNaN = require_isNaN();
	var _isBounded = requireIsBounded();
	var _isFinite = require_isFinite();
	var _isNegative = requireIsNegative();
	var _isNumeric = requireIsNumeric();
	var _isPositive = requireIsPositive();
	var _isPrime = requireIsPrime();
	var _isZero = requireIsZero();
	var _numeric = requireNumeric();
	var _oct = requireOct();
	var _print = requirePrint();
	var _typeOf = requireTypeOf();
	var _solveODE = requireSolveODE();
	embeddedDocs.embeddedDocs = {
	  // construction functions
	  bignumber: _bignumber.bignumberDocs,
	  bigint: _bigint.bigintDocs,
	  boolean: _boolean.booleanDocs,
	  complex: _complex.complexDocs,
	  createUnit: _createUnit.createUnitDocs,
	  fraction: _fraction.fractionDocs,
	  index: _index.indexDocs,
	  matrix: _matrix.matrixDocs,
	  number: _number.numberDocs,
	  sparse: _sparse.sparseDocs,
	  splitUnit: _splitUnit.splitUnitDocs,
	  string: _string.stringDocs,
	  unit: _unit.unitDocs,
	  // constants
	  e: _e.eDocs,
	  E: _e.eDocs,
	  false: _false.falseDocs,
	  i: _i.iDocs,
	  Infinity: _Infinity.InfinityDocs,
	  LN2: _LN2.LN2Docs,
	  LN10: _LN.LN10Docs,
	  LOG2E: _LOG2E.LOG2EDocs,
	  LOG10E: _LOG10E.LOG10EDocs,
	  NaN: _NaN.NaNDocs,
	  null: _null.nullDocs,
	  pi: _pi.piDocs,
	  PI: _pi.piDocs,
	  phi: _phi.phiDocs,
	  SQRT1_2: _SQRT1_.SQRT12Docs,
	  SQRT2: _SQRT.SQRT2Docs,
	  tau: _tau.tauDocs,
	  true: _true.trueDocs,
	  version: _version.versionDocs,
	  // physical constants
	  // TODO: more detailed docs for physical constants
	  speedOfLight: {
	    description: 'Speed of light in vacuum',
	    examples: ['speedOfLight']
	  },
	  gravitationConstant: {
	    description: 'Newtonian constant of gravitation',
	    examples: ['gravitationConstant']
	  },
	  planckConstant: {
	    description: 'Planck constant',
	    examples: ['planckConstant']
	  },
	  reducedPlanckConstant: {
	    description: 'Reduced Planck constant',
	    examples: ['reducedPlanckConstant']
	  },
	  magneticConstant: {
	    description: 'Magnetic constant (vacuum permeability)',
	    examples: ['magneticConstant']
	  },
	  electricConstant: {
	    description: 'Electric constant (vacuum permeability)',
	    examples: ['electricConstant']
	  },
	  vacuumImpedance: {
	    description: 'Characteristic impedance of vacuum',
	    examples: ['vacuumImpedance']
	  },
	  coulomb: {
	    description: 'Coulomb\'s constant. Deprecated in favor of coulombConstant',
	    examples: ['coulombConstant']
	  },
	  coulombConstant: {
	    description: 'Coulomb\'s constant',
	    examples: ['coulombConstant']
	  },
	  elementaryCharge: {
	    description: 'Elementary charge',
	    examples: ['elementaryCharge']
	  },
	  bohrMagneton: {
	    description: 'Bohr magneton',
	    examples: ['bohrMagneton']
	  },
	  conductanceQuantum: {
	    description: 'Conductance quantum',
	    examples: ['conductanceQuantum']
	  },
	  inverseConductanceQuantum: {
	    description: 'Inverse conductance quantum',
	    examples: ['inverseConductanceQuantum']
	  },
	  // josephson: {description: 'Josephson constant', examples: ['josephson']},
	  magneticFluxQuantum: {
	    description: 'Magnetic flux quantum',
	    examples: ['magneticFluxQuantum']
	  },
	  nuclearMagneton: {
	    description: 'Nuclear magneton',
	    examples: ['nuclearMagneton']
	  },
	  klitzing: {
	    description: 'Von Klitzing constant',
	    examples: ['klitzing']
	  },
	  bohrRadius: {
	    description: 'Bohr radius',
	    examples: ['bohrRadius']
	  },
	  classicalElectronRadius: {
	    description: 'Classical electron radius',
	    examples: ['classicalElectronRadius']
	  },
	  electronMass: {
	    description: 'Electron mass',
	    examples: ['electronMass']
	  },
	  fermiCoupling: {
	    description: 'Fermi coupling constant',
	    examples: ['fermiCoupling']
	  },
	  fineStructure: {
	    description: 'Fine-structure constant',
	    examples: ['fineStructure']
	  },
	  hartreeEnergy: {
	    description: 'Hartree energy',
	    examples: ['hartreeEnergy']
	  },
	  protonMass: {
	    description: 'Proton mass',
	    examples: ['protonMass']
	  },
	  deuteronMass: {
	    description: 'Deuteron Mass',
	    examples: ['deuteronMass']
	  },
	  neutronMass: {
	    description: 'Neutron mass',
	    examples: ['neutronMass']
	  },
	  quantumOfCirculation: {
	    description: 'Quantum of circulation',
	    examples: ['quantumOfCirculation']
	  },
	  rydberg: {
	    description: 'Rydberg constant',
	    examples: ['rydberg']
	  },
	  thomsonCrossSection: {
	    description: 'Thomson cross section',
	    examples: ['thomsonCrossSection']
	  },
	  weakMixingAngle: {
	    description: 'Weak mixing angle',
	    examples: ['weakMixingAngle']
	  },
	  efimovFactor: {
	    description: 'Efimov factor',
	    examples: ['efimovFactor']
	  },
	  atomicMass: {
	    description: 'Atomic mass constant',
	    examples: ['atomicMass']
	  },
	  avogadro: {
	    description: 'Avogadro\'s number',
	    examples: ['avogadro']
	  },
	  boltzmann: {
	    description: 'Boltzmann constant',
	    examples: ['boltzmann']
	  },
	  faraday: {
	    description: 'Faraday constant',
	    examples: ['faraday']
	  },
	  firstRadiation: {
	    description: 'First radiation constant',
	    examples: ['firstRadiation']
	  },
	  loschmidt: {
	    description: 'Loschmidt constant at T=273.15 K and p=101.325 kPa',
	    examples: ['loschmidt']
	  },
	  gasConstant: {
	    description: 'Gas constant',
	    examples: ['gasConstant']
	  },
	  molarPlanckConstant: {
	    description: 'Molar Planck constant',
	    examples: ['molarPlanckConstant']
	  },
	  molarVolume: {
	    description: 'Molar volume of an ideal gas at T=273.15 K and p=101.325 kPa',
	    examples: ['molarVolume']
	  },
	  sackurTetrode: {
	    description: 'Sackur-Tetrode constant at T=1 K and p=101.325 kPa',
	    examples: ['sackurTetrode']
	  },
	  secondRadiation: {
	    description: 'Second radiation constant',
	    examples: ['secondRadiation']
	  },
	  stefanBoltzmann: {
	    description: 'Stefan-Boltzmann constant',
	    examples: ['stefanBoltzmann']
	  },
	  wienDisplacement: {
	    description: 'Wien displacement law constant',
	    examples: ['wienDisplacement']
	  },
	  // spectralRadiance: {description: 'First radiation constant for spectral radiance', examples: ['spectralRadiance']},

	  molarMass: {
	    description: 'Molar mass constant',
	    examples: ['molarMass']
	  },
	  molarMassC12: {
	    description: 'Molar mass constant of carbon-12',
	    examples: ['molarMassC12']
	  },
	  gravity: {
	    description: 'Standard acceleration of gravity (standard acceleration of free-fall on Earth)',
	    examples: ['gravity']
	  },
	  planckLength: {
	    description: 'Planck length',
	    examples: ['planckLength']
	  },
	  planckMass: {
	    description: 'Planck mass',
	    examples: ['planckMass']
	  },
	  planckTime: {
	    description: 'Planck time',
	    examples: ['planckTime']
	  },
	  planckCharge: {
	    description: 'Planck charge',
	    examples: ['planckCharge']
	  },
	  planckTemperature: {
	    description: 'Planck temperature',
	    examples: ['planckTemperature']
	  },
	  // functions - algebra
	  derivative: _derivative.derivativeDocs,
	  lsolve: _lsolve.lsolveDocs,
	  lsolveAll: _lsolveAll.lsolveAllDocs,
	  lup: _lup.lupDocs,
	  lusolve: _lusolve.lusolveDocs,
	  leafCount: _leafCount.leafCountDocs,
	  polynomialRoot: _polynomialRoot.polynomialRootDocs,
	  resolve: _resolve.resolveDocs,
	  simplify: _simplify.simplifyDocs,
	  simplifyConstant: _simplifyConstant.simplifyConstantDocs,
	  simplifyCore: _simplifyCore.simplifyCoreDocs,
	  symbolicEqual: _symbolicEqual.symbolicEqualDocs,
	  rationalize: _rationalize.rationalizeDocs,
	  slu: _slu.sluDocs,
	  usolve: _usolve.usolveDocs,
	  usolveAll: _usolveAll.usolveAllDocs,
	  qr: _qr.qrDocs,
	  // functions - arithmetic
	  abs: _abs.absDocs,
	  add: _add.addDocs,
	  cbrt: _cbrt.cbrtDocs,
	  ceil: _ceil.ceilDocs,
	  cube: _cube.cubeDocs,
	  divide: _divide.divideDocs,
	  dotDivide: _dotDivide.dotDivideDocs,
	  dotMultiply: _dotMultiply.dotMultiplyDocs,
	  dotPow: _dotPow.dotPowDocs,
	  exp: _exp.expDocs,
	  expm: _expm.expmDocs,
	  expm1: _expm2.expm1Docs,
	  fix: _fix.fixDocs,
	  floor: _floor.floorDocs,
	  gcd: _gcd.gcdDocs,
	  hypot: _hypot.hypotDocs,
	  lcm: _lcm.lcmDocs,
	  log: _log.logDocs,
	  log2: _log3.log2Docs,
	  log1p: _log1p.log1pDocs,
	  log10: _log2.log10Docs,
	  mod: _mod.modDocs,
	  multiply: _multiply.multiplyDocs,
	  norm: _norm.normDocs,
	  nthRoot: _nthRoot.nthRootDocs,
	  nthRoots: _nthRoots.nthRootsDocs,
	  pow: _pow.powDocs,
	  round: _round.roundDocs,
	  sign: _sign.signDocs,
	  sqrt: _sqrt.sqrtDocs,
	  sqrtm: _sqrtm.sqrtmDocs,
	  square: _square.squareDocs,
	  subtract: _subtract.subtractDocs,
	  unaryMinus: _unaryMinus.unaryMinusDocs,
	  unaryPlus: _unaryPlus.unaryPlusDocs,
	  xgcd: _xgcd.xgcdDocs,
	  invmod: _invmod.invmodDocs,
	  num: _num.numDocs,
	  den: _den.denDocs,
	  // functions - bitwise
	  bitAnd: _bitAnd.bitAndDocs,
	  bitNot: _bitNot.bitNotDocs,
	  bitOr: _bitOr.bitOrDocs,
	  bitXor: _bitXor.bitXorDocs,
	  leftShift: _leftShift.leftShiftDocs,
	  rightArithShift: _rightArithShift.rightArithShiftDocs,
	  rightLogShift: _rightLogShift.rightLogShiftDocs,
	  // functions - combinatorics
	  bellNumbers: _bellNumbers.bellNumbersDocs,
	  catalan: _catalan.catalanDocs,
	  composition: _composition.compositionDocs,
	  stirlingS2: _stirlingS.stirlingS2Docs,
	  // functions - core
	  config: _config.configDocs,
	  import: _import.importDocs,
	  typed: _typed.typedDocs,
	  // functions - complex
	  arg: _arg.argDocs,
	  conj: _conj.conjDocs,
	  re: _re.reDocs,
	  im: _im.imDocs,
	  // functions - expression
	  evaluate: _evaluate.evaluateDocs,
	  help: _help.helpDocs,
	  parse: _parse.parseDocs,
	  parser: _parser.parserDocs,
	  compile: _compile.compileDocs,
	  // functions - geometry
	  distance: _distance.distanceDocs,
	  intersect: _intersect.intersectDocs,
	  // functions - logical
	  and: _and.andDocs,
	  not: _not.notDocs,
	  nullish: _nullish.nullishDocs,
	  or: _or.orDocs,
	  xor: _xor.xorDocs,
	  // functions - matrix
	  mapSlices: _mapSlices.mapSlicesDocs,
	  concat: _concat.concatDocs,
	  count: _count.countDocs,
	  cross: _cross.crossDocs,
	  column: _column.columnDocs,
	  ctranspose: _ctranspose.ctransposeDocs,
	  det: _det.detDocs,
	  diag: _diag.diagDocs,
	  diff: _diff.diffDocs,
	  dot: _dot.dotDocs,
	  getMatrixDataType: _getMatrixDataType.getMatrixDataTypeDocs,
	  identity: _identity.identityDocs,
	  filter: _filter.filterDocs,
	  flatten: _flatten.flattenDocs,
	  forEach: _forEach.forEachDocs,
	  inv: _inv.invDocs,
	  pinv: _pinv.pinvDocs,
	  eigs: _eigs.eigsDocs,
	  kron: _kron.kronDocs,
	  matrixFromFunction: _matrixFromFunction.matrixFromFunctionDocs,
	  matrixFromRows: _matrixFromRows.matrixFromRowsDocs,
	  matrixFromColumns: _matrixFromColumns.matrixFromColumnsDocs,
	  map: _map.mapDocs,
	  ones: _ones.onesDocs,
	  partitionSelect: _partitionSelect.partitionSelectDocs,
	  range: _range.rangeDocs,
	  resize: _resize.resizeDocs,
	  reshape: _reshape.reshapeDocs,
	  rotate: _rotate.rotateDocs,
	  rotationMatrix: _rotationMatrix.rotationMatrixDocs,
	  row: _row.rowDocs,
	  size: _size.sizeDocs,
	  sort: _sort.sortDocs,
	  squeeze: _squeeze.squeezeDocs,
	  subset: _subset.subsetDocs,
	  trace: _trace.traceDocs,
	  transpose: _transpose.transposeDocs,
	  zeros: _zeros.zerosDocs,
	  fft: _fft.fftDocs,
	  ifft: _ifft.ifftDocs,
	  sylvester: _sylvester.sylvesterDocs,
	  schur: _schur.schurDocs,
	  lyap: _lyap.lyapDocs,
	  // functions - numeric
	  solveODE: _solveODE.solveODEDocs,
	  // functions - probability
	  bernoulli: _bernoulli.bernoulliDocs,
	  combinations: _combinations.combinationsDocs,
	  combinationsWithRep: _combinationsWithRep.combinationsWithRepDocs,
	  // distribution: distributionDocs,
	  factorial: _factorial.factorialDocs,
	  gamma: _gamma.gammaDocs,
	  kldivergence: _kldivergence.kldivergenceDocs,
	  lgamma: _lgamma.lgammaDocs,
	  multinomial: _multinomial.multinomialDocs,
	  permutations: _permutations.permutationsDocs,
	  pickRandom: _pickRandom.pickRandomDocs,
	  random: _random.randomDocs,
	  randomInt: _randomInt.randomIntDocs,
	  // functions - relational
	  compare: _compare.compareDocs,
	  compareNatural: _compareNatural.compareNaturalDocs,
	  compareText: _compareText.compareTextDocs,
	  deepEqual: _deepEqual.deepEqualDocs,
	  equal: _equal.equalDocs,
	  equalText: _equalText.equalTextDocs,
	  larger: _larger.largerDocs,
	  largerEq: _largerEq.largerEqDocs,
	  smaller: _smaller.smallerDocs,
	  smallerEq: _smallerEq.smallerEqDocs,
	  unequal: _unequal.unequalDocs,
	  // functions - set
	  setCartesian: _setCartesian.setCartesianDocs,
	  setDifference: _setDifference.setDifferenceDocs,
	  setDistinct: _setDistinct.setDistinctDocs,
	  setIntersect: _setIntersect.setIntersectDocs,
	  setIsSubset: _setIsSubset.setIsSubsetDocs,
	  setMultiplicity: _setMultiplicity.setMultiplicityDocs,
	  setPowerset: _setPowerset.setPowersetDocs,
	  setSize: _setSize.setSizeDocs,
	  setSymDifference: _setSymDifference.setSymDifferenceDocs,
	  setUnion: _setUnion.setUnionDocs,
	  // functions - signal
	  zpk2tf: _zpk2tf.zpk2tfDocs,
	  freqz: _freqz.freqzDocs,
	  // functions - special
	  erf: _erf.erfDocs,
	  zeta: _zeta.zetaDocs,
	  // functions - statistics
	  cumsum: _cumsum.cumSumDocs,
	  mad: _mad.madDocs,
	  max: _max.maxDocs,
	  mean: _mean.meanDocs,
	  median: _median.medianDocs,
	  min: _min.minDocs,
	  mode: _mode.modeDocs,
	  prod: _prod.prodDocs,
	  quantileSeq: _quantileSeq.quantileSeqDocs,
	  std: _std.stdDocs,
	  sum: _sum.sumDocs,
	  variance: _variance.varianceDocs,
	  corr: _corr.corrDocs,
	  // functions - trigonometry
	  acos: _acos.acosDocs,
	  acosh: _acosh.acoshDocs,
	  acot: _acot.acotDocs,
	  acoth: _acoth.acothDocs,
	  acsc: _acsc.acscDocs,
	  acsch: _acsch.acschDocs,
	  asec: _asec.asecDocs,
	  asech: _asech.asechDocs,
	  asin: _asin.asinDocs,
	  asinh: _asinh.asinhDocs,
	  atan: _atan.atanDocs,
	  atanh: _atanh.atanhDocs,
	  atan2: _atan2.atan2Docs,
	  cos: _cos.cosDocs,
	  cosh: _cosh.coshDocs,
	  cot: _cot.cotDocs,
	  coth: _coth.cothDocs,
	  csc: _csc.cscDocs,
	  csch: _csch.cschDocs,
	  sec: _sec.secDocs,
	  sech: _sech.sechDocs,
	  sin: _sin.sinDocs,
	  sinh: _sinh.sinhDocs,
	  tan: _tan.tanDocs,
	  tanh: _tanh.tanhDocs,
	  // functions - units
	  to: _to.toDocs,
	  toBest: _toBest.toBestDocs,
	  // functions - utils
	  clone: _clone.cloneDocs,
	  format: _format.formatDocs,
	  bin: _bin.binDocs,
	  oct: _oct.octDocs,
	  hex: _hex.hexDocs,
	  isNaN: _isNaN.isNaNDocs,
	  isBounded: _isBounded.isBoundedDocs,
	  isFinite: _isFinite.isFiniteDocs,
	  isInteger: _isInteger.isIntegerDocs,
	  isNegative: _isNegative.isNegativeDocs,
	  isNumeric: _isNumeric.isNumericDocs,
	  hasNumericValue: _hasNumericValue.hasNumericValueDocs,
	  isPositive: _isPositive.isPositiveDocs,
	  isPrime: _isPrime.isPrimeDocs,
	  isZero: _isZero.isZeroDocs,
	  print: _print.printDocs,
	  typeOf: _typeOf.typeOfDocs,
	  numeric: _numeric.numericDocs
	};
	return embeddedDocs;
}

var hasRequiredHelp;

function requireHelp () {
	if (hasRequiredHelp) return help$1;
	hasRequiredHelp = 1;

	Object.defineProperty(help$1, "__esModule", {
	  value: true
	});
	help$1.createHelp = void 0;
	var _factory = requireFactory$b();
	var _customs = requireCustoms();
	var _embeddedDocs = requireEmbeddedDocs();
	var _object = requireObject$3();
	const name = 'help';
	const dependencies = ['typed', 'mathWithTransform', 'Help'];
	help$1.createHelp = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    mathWithTransform,
	    Help
	  } = _ref;
	  /**
	   * Retrieve help on a function or data type.
	   * Help files are retrieved from the embedded documentation in math.docs.
	   *
	   * Syntax:
	   *
	   *    math.help(search)
	   *
	   * Examples:
	   *
	   *    console.log(math.help('sin').toString())
	   *    console.log(math.help(math.add).toString())
	   *    console.log(math.help(math.add).toJSON())
	   *
	   * @param {Function | string | Object} search   A function or function name
	   *                                              for which to get help
	   * @return {Help} A help object
	   */
	  return typed(name, {
	    any: function (search) {
	      let prop;
	      let searchName = search;
	      if (typeof search !== 'string') {
	        for (prop in mathWithTransform) {
	          // search in functions and constants
	          if ((0, _object.hasOwnProperty)(mathWithTransform, prop) && search === mathWithTransform[prop]) {
	            searchName = prop;
	            break;
	          }
	        }

	        /* TODO: implement help for data types
	         if (!text) {
	         // search data type
	         for (prop in math.type) {
	         if (hasOwnProperty(math, prop)) {
	         if (search === math.type[prop]) {
	         text = prop
	         break
	         }
	         }
	         }
	         }
	         */
	      }
	      const doc = (0, _customs.getSafeProperty)(_embeddedDocs.embeddedDocs, searchName);
	      if (!doc) {
	        const searchText = typeof searchName === 'function' ? searchName.name : searchName;
	        throw new Error('No documentation found on "' + searchText + '"');
	      }
	      return new Help(doc);
	    }
	  });
	});
	return help$1;
}

var chain = {};

var hasRequiredChain;

function requireChain () {
	if (hasRequiredChain) return chain;
	hasRequiredChain = 1;

	Object.defineProperty(chain, "__esModule", {
	  value: true
	});
	chain.createChain = void 0;
	var _factory = requireFactory$b();
	const name = 'chain';
	const dependencies = ['typed', 'Chain'];
	chain.createChain = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Chain
	  } = _ref;
	  /**
	   * Wrap any value in a chain, allowing to perform chained operations on
	   * the value.
	   *
	   * All methods available in the math.js library can be called upon the chain,
	   * and then will be evaluated with the value itself as first argument.
	   * The chain can be closed by executing `chain.done()`, which returns
	   * the final value.
	   *
	   * The chain has a number of special functions:
	   *
	   * - `done()`     Finalize the chain and return the chain's value.
	   * - `valueOf()`  The same as `done()`
	   * - `toString()` Executes `math.format()` onto the chain's value, returning
	   *                a string representation of the value.
	   *
	   * Syntax:
	   *
	   *    math.chain(value)
	   *
	   * Examples:
	   *
	   *     math.chain(3)
	   *         .add(4)
	   *         .subtract(2)
	   *         .done()     // 5
	   *
	   *     math.chain( [[1, 2], [3, 4]] )
	   *         .subset(math.index(0, 0), 8)
	   *         .multiply(3)
	   *         .done()     // [[24, 6], [9, 12]]
	   *
	   * @param {*} [value]   A value of any type on which to start a chained operation.
	   * @return {math.Chain} The created chain
	   */
	  return typed(name, {
	    '': function () {
	      return new Chain();
	    },
	    any: function (value) {
	      return new Chain(value);
	    }
	  });
	});
	return chain;
}

var det = {};

var hasRequiredDet;

function requireDet () {
	if (hasRequiredDet) return det;
	hasRequiredDet = 1;

	Object.defineProperty(det, "__esModule", {
	  value: true
	});
	det.createDet = void 0;
	var _is = requireIs();
	var _object = requireObject$3();
	var _string = requireString$2();
	var _factory = requireFactory$b();
	const name = 'det';
	const dependencies = ['typed', 'matrix', 'subtractScalar', 'multiply', 'divideScalar', 'isZero', 'unaryMinus'];
	det.createDet = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    subtractScalar,
	    multiply,
	    divideScalar,
	    isZero,
	    unaryMinus
	  } = _ref;
	  /**
	   * Calculate the determinant of a matrix.
	   *
	   * Syntax:
	   *
	   *    math.det(x)
	   *
	   * Examples:
	   *
	   *    math.det([[1, 2], [3, 4]]) // returns -2
	   *
	   *    const A = [
	   *      [-2, 2, 3],
	   *      [-1, 1, 3],
	   *      [2, 0, -1]
	   *    ]
	   *    math.det(A) // returns 6
	   *
	   * See also:
	   *
	   *    inv
	   *
	   * @param {Array | Matrix} x  A matrix
	   * @return {number} The determinant of `x`
	   */
	  return typed(name, {
	    any: function (x) {
	      return (0, _object.clone)(x);
	    },
	    'Array | Matrix': function det(x) {
	      let size;
	      if ((0, _is.isMatrix)(x)) {
	        size = x.size();
	      } else if (Array.isArray(x)) {
	        x = matrix(x);
	        size = x.size();
	      } else {
	        // a scalar
	        size = [];
	      }
	      switch (size.length) {
	        case 0:
	          // scalar
	          return (0, _object.clone)(x);
	        case 1:
	          // vector
	          if (size[0] === 1) {
	            return (0, _object.clone)(x.valueOf()[0]);
	          }
	          if (size[0] === 0) {
	            return 1; // det of an empty matrix is per definition 1
	          } else {
	            throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	          }
	        case 2:
	          {
	            // two-dimensional array
	            const rows = size[0];
	            const cols = size[1];
	            if (rows === cols) {
	              return _det(x.clone().valueOf(), rows);
	            }
	            if (cols === 0) {
	              return 1; // det of an empty matrix is per definition 1
	            } else {
	              throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	            }
	          }
	        default:
	          // multi dimensional array
	          throw new RangeError('Matrix must be two dimensional ' + '(size: ' + (0, _string.format)(size) + ')');
	      }
	    }
	  });

	  /**
	   * Calculate the determinant of a matrix
	   * @param {Array[]} matrix  A square, two dimensional matrix
	   * @param {number} rows     Number of rows of the matrix (zero-based)
	   * @param {number} cols     Number of columns of the matrix (zero-based)
	   * @returns {number} det
	   * @private
	   */
	  function _det(matrix, rows, cols) {
	    if (rows === 1) {
	      // this is a 1 x 1 matrix
	      return (0, _object.clone)(matrix[0][0]);
	    } else if (rows === 2) {
	      // this is a 2 x 2 matrix
	      // the determinant of [a11,a12;a21,a22] is det = a11*a22-a21*a12
	      return subtractScalar(multiply(matrix[0][0], matrix[1][1]), multiply(matrix[1][0], matrix[0][1]));
	    } else {
	      // Bareiss algorithm
	      // this algorithm have same complexity as LUP decomposition (O(n^3))
	      // but it preserve precision of floating point more relative to the LUP decomposition
	      let negated = false;
	      const rowIndices = new Array(rows).fill(0).map((_, i) => i); // matrix index of row i
	      for (let k = 0; k < rows; k++) {
	        let k_ = rowIndices[k];
	        if (isZero(matrix[k_][k])) {
	          let _k;
	          for (_k = k + 1; _k < rows; _k++) {
	            if (!isZero(matrix[rowIndices[_k]][k])) {
	              k_ = rowIndices[_k];
	              rowIndices[_k] = rowIndices[k];
	              rowIndices[k] = k_;
	              negated = !negated;
	              break;
	            }
	          }
	          if (_k === rows) return matrix[k_][k]; // some zero of the type
	        }
	        const piv = matrix[k_][k];
	        const piv_ = k === 0 ? 1 : matrix[rowIndices[k - 1]][k - 1];
	        for (let i = k + 1; i < rows; i++) {
	          const i_ = rowIndices[i];
	          for (let j = k + 1; j < rows; j++) {
	            matrix[i_][j] = divideScalar(subtractScalar(multiply(matrix[i_][j], piv), multiply(matrix[i_][k], matrix[k_][j])), piv_);
	          }
	        }
	      }
	      const det = matrix[rowIndices[rows - 1]][rows - 1];
	      return negated ? unaryMinus(det) : det;
	    }
	  }
	});
	return det;
}

var inv = {};

var hasRequiredInv;

function requireInv () {
	if (hasRequiredInv) return inv;
	hasRequiredInv = 1;

	Object.defineProperty(inv, "__esModule", {
	  value: true
	});
	inv.createInv = void 0;
	var _is = requireIs();
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _string = requireString$2();
	const name = 'inv';
	const dependencies = ['typed', 'matrix', 'divideScalar', 'addScalar', 'multiply', 'unaryMinus', 'det', 'identity', 'abs'];
	inv.createInv = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    divideScalar,
	    addScalar,
	    multiply,
	    unaryMinus,
	    det,
	    identity,
	    abs
	  } = _ref;
	  /**
	   * Calculate the inverse of a square matrix.
	   *
	   * Syntax:
	   *
	   *     math.inv(x)
	   *
	   * Examples:
	   *
	   *     math.inv([[1, 2], [3, 4]])  // returns [[-2, 1], [1.5, -0.5]]
	   *     math.inv(4)                 // returns 0.25
	   *     1 / 4                       // returns 0.25
	   *
	   * See also:
	   *
	   *     det, transpose
	   *
	   * @param {number | Complex | Array | Matrix} x     Matrix to be inversed
	   * @return {number | Complex | Array | Matrix} The inverse of `x`.
	   */
	  return typed(name, {
	    'Array | Matrix': function (x) {
	      const size = (0, _is.isMatrix)(x) ? x.size() : (0, _array.arraySize)(x);
	      switch (size.length) {
	        case 1:
	          // vector
	          if (size[0] === 1) {
	            if ((0, _is.isMatrix)(x)) {
	              return matrix([divideScalar(1, x.valueOf()[0])]);
	            } else {
	              return [divideScalar(1, x[0])];
	            }
	          } else {
	            throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	          }
	        case 2:
	          // two dimensional array
	          {
	            const rows = size[0];
	            const cols = size[1];
	            if (rows === cols) {
	              if ((0, _is.isMatrix)(x)) {
	                return matrix(_inv(x.valueOf(), rows, cols), x.storage());
	              } else {
	                // return an Array
	                return _inv(x, rows, cols);
	              }
	            } else {
	              throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	            }
	          }
	        default:
	          // multi dimensional array
	          throw new RangeError('Matrix must be two dimensional ' + '(size: ' + (0, _string.format)(size) + ')');
	      }
	    },
	    any: function (x) {
	      // scalar
	      return divideScalar(1, x); // FIXME: create a BigNumber one when configured for bignumbers
	    }
	  });

	  /**
	   * Calculate the inverse of a square matrix
	   * @param {Array[]} mat     A square matrix
	   * @param {number} rows     Number of rows
	   * @param {number} cols     Number of columns, must equal rows
	   * @return {Array[]} inv    Inverse matrix
	   * @private
	   */
	  function _inv(mat, rows, cols) {
	    let r, s, f, value, temp;
	    if (rows === 1) {
	      // this is a 1 x 1 matrix
	      value = mat[0][0];
	      if (value === 0) {
	        throw Error('Cannot calculate inverse, determinant is zero');
	      }
	      return [[divideScalar(1, value)]];
	    } else if (rows === 2) {
	      // this is a 2 x 2 matrix
	      const d = det(mat);
	      if (d === 0) {
	        throw Error('Cannot calculate inverse, determinant is zero');
	      }
	      return [[divideScalar(mat[1][1], d), divideScalar(unaryMinus(mat[0][1]), d)], [divideScalar(unaryMinus(mat[1][0]), d), divideScalar(mat[0][0], d)]];
	    } else {
	      // this is a matrix of 3 x 3 or larger
	      // calculate inverse using gauss-jordan elimination
	      //      https://en.wikipedia.org/wiki/Gaussian_elimination
	      //      http://mathworld.wolfram.com/MatrixInverse.html
	      //      http://math.uww.edu/~mcfarlat/inverse.htm

	      // make a copy of the matrix (only the arrays, not of the elements)
	      const A = mat.concat();
	      for (r = 0; r < rows; r++) {
	        A[r] = A[r].concat();
	      }

	      // create an identity matrix which in the end will contain the
	      // matrix inverse
	      const B = identity(rows).valueOf();

	      // loop over all columns, and perform row reductions
	      for (let c = 0; c < cols; c++) {
	        // Pivoting: Swap row c with row r, where row r contains the largest element A[r][c]
	        let ABig = abs(A[c][c]);
	        let rBig = c;
	        r = c + 1;
	        while (r < rows) {
	          if (abs(A[r][c]) > ABig) {
	            ABig = abs(A[r][c]);
	            rBig = r;
	          }
	          r++;
	        }
	        if (ABig === 0) {
	          throw Error('Cannot calculate inverse, determinant is zero');
	        }
	        r = rBig;
	        if (r !== c) {
	          temp = A[c];
	          A[c] = A[r];
	          A[r] = temp;
	          temp = B[c];
	          B[c] = B[r];
	          B[r] = temp;
	        }

	        // eliminate non-zero values on the other rows at column c
	        const Ac = A[c];
	        const Bc = B[c];
	        for (r = 0; r < rows; r++) {
	          const Ar = A[r];
	          const Br = B[r];
	          if (r !== c) {
	            // eliminate value at column c and row r
	            if (Ar[c] !== 0) {
	              f = divideScalar(unaryMinus(Ar[c]), Ac[c]);

	              // add (f * row c) to row r to eliminate the value
	              // at column c
	              for (s = c; s < cols; s++) {
	                Ar[s] = addScalar(Ar[s], multiply(f, Ac[s]));
	              }
	              for (s = 0; s < cols; s++) {
	                Br[s] = addScalar(Br[s], multiply(f, Bc[s]));
	              }
	            }
	          } else {
	            // normalize value at Acc to 1,
	            // divide each value on row r with the value at Acc
	            f = Ac[c];
	            for (s = c; s < cols; s++) {
	              Ar[s] = divideScalar(Ar[s], f);
	            }
	            for (s = 0; s < cols; s++) {
	              Br[s] = divideScalar(Br[s], f);
	            }
	          }
	        }
	      }
	      return B;
	    }
	  }
	});
	return inv;
}

var pinv = {};

var hasRequiredPinv;

function requirePinv () {
	if (hasRequiredPinv) return pinv;
	hasRequiredPinv = 1;

	Object.defineProperty(pinv, "__esModule", {
	  value: true
	});
	pinv.createPinv = void 0;
	var _is = requireIs();
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _string = requireString$2();
	var _object = requireObject$3();
	const name = 'pinv';
	const dependencies = ['typed', 'matrix', 'inv', 'deepEqual', 'equal', 'dotDivide', 'dot', 'ctranspose', 'divideScalar', 'multiply', 'add', 'Complex'];
	pinv.createPinv = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    inv,
	    deepEqual,
	    equal,
	    dotDivide,
	    dot,
	    ctranspose,
	    divideScalar,
	    multiply,
	    add,
	    Complex
	  } = _ref;
	  /**
	   * Calculate the Moore–Penrose inverse of a matrix.
	   *
	   * Syntax:
	   *
	   *     math.pinv(x)
	   *
	   * Examples:
	   *
	   *     math.pinv([[1, 2], [3, 4]])          // returns [[-2, 1], [1.5, -0.5]]
	   *     math.pinv([[1, 0], [0, 1], [0, 1]])  // returns [[1, 0, 0], [0, 0.5, 0.5]]
	   *     math.pinv(4)                         // returns 0.25
	   *
	   * See also:
	   *
	   *     inv
	   *
	   * @param {number | Complex | Array | Matrix} x     Matrix to be inversed
	   * @return {number | Complex | Array | Matrix} The inverse of `x`.
	   */
	  return typed(name, {
	    'Array | Matrix': function (x) {
	      const size = (0, _is.isMatrix)(x) ? x.size() : (0, _array.arraySize)(x);
	      switch (size.length) {
	        case 1:
	          // vector
	          if (_isZeros(x)) return ctranspose(x); // null vector
	          if (size[0] === 1) {
	            return inv(x); // invertible matrix
	          } else {
	            return dotDivide(ctranspose(x), dot(x, x));
	          }
	        case 2:
	          // two dimensional array
	          {
	            if (_isZeros(x)) return ctranspose(x); // zero matrixx
	            const rows = size[0];
	            const cols = size[1];
	            if (rows === cols) {
	              try {
	                return inv(x); // invertible matrix
	              } catch (err) {
	                if (err instanceof Error && err.message.match(/Cannot calculate inverse, determinant is zero/)) ; else {
	                  throw err;
	                }
	              }
	            }
	            if ((0, _is.isMatrix)(x)) {
	              return matrix(_pinv(x.valueOf(), rows, cols), x.storage());
	            } else {
	              // return an Array
	              return _pinv(x, rows, cols);
	            }
	          }
	        default:
	          // multi dimensional array
	          throw new RangeError('Matrix must be two dimensional ' + '(size: ' + (0, _string.format)(size) + ')');
	      }
	    },
	    any: function (x) {
	      // scalar
	      if (equal(x, 0)) return (0, _object.clone)(x); // zero
	      return divideScalar(1, x);
	    }
	  });

	  /**
	   * Calculate the Moore–Penrose inverse of a matrix
	   * @param {Array[]} mat     A matrix
	   * @param {number} rows     Number of rows
	   * @param {number} cols     Number of columns
	   * @return {Array[]} pinv    Pseudoinverse matrix
	   * @private
	   */
	  function _pinv(mat, rows, cols) {
	    const {
	      C,
	      F
	    } = _rankFact(mat, rows, cols); // TODO: Use SVD instead (may improve precision)
	    const Cpinv = multiply(inv(multiply(ctranspose(C), C)), ctranspose(C));
	    const Fpinv = multiply(ctranspose(F), inv(multiply(F, ctranspose(F))));
	    return multiply(Fpinv, Cpinv);
	  }

	  /**
	   * Calculate the reduced row echelon form of a matrix
	   *
	   * Modified from https://rosettacode.org/wiki/Reduced_row_echelon_form
	   *
	   * @param {Array[]} mat     A matrix
	   * @param {number} rows     Number of rows
	   * @param {number} cols     Number of columns
	   * @return {Array[]}        Reduced row echelon form
	   * @private
	   */
	  function _rref(mat, rows, cols) {
	    const M = (0, _object.clone)(mat);
	    let lead = 0;
	    for (let r = 0; r < rows; r++) {
	      if (cols <= lead) {
	        return M;
	      }
	      let i = r;
	      while (_isZero(M[i][lead])) {
	        i++;
	        if (rows === i) {
	          i = r;
	          lead++;
	          if (cols === lead) {
	            return M;
	          }
	        }
	      }
	      [M[i], M[r]] = [M[r], M[i]];
	      let val = M[r][lead];
	      for (let j = 0; j < cols; j++) {
	        M[r][j] = dotDivide(M[r][j], val);
	      }
	      for (let i = 0; i < rows; i++) {
	        if (i === r) continue;
	        val = M[i][lead];
	        for (let j = 0; j < cols; j++) {
	          M[i][j] = add(M[i][j], multiply(-1, multiply(val, M[r][j])));
	        }
	      }
	      lead++;
	    }
	    return M;
	  }

	  /**
	   * Calculate the rank factorization of a matrix
	   *
	   * @param {Array[]} mat                  A matrix (M)
	   * @param {number} rows                  Number of rows
	   * @param {number} cols                  Number of columns
	   * @return {{C: Array, F: Array}}        rank factorization where M = C F
	   * @private
	   */
	  function _rankFact(mat, rows, cols) {
	    const rref = _rref(mat, rows, cols);
	    const C = mat.map((_, i) => _.filter((_, j) => j < rows && !_isZero(dot(rref[j], rref[j]))));
	    const F = rref.filter((_, i) => !_isZero(dot(rref[i], rref[i])));
	    return {
	      C,
	      F
	    };
	  }
	  function _isZero(x) {
	    return equal(add(x, Complex(1, 1)), add(0, Complex(1, 1)));
	  }
	  function _isZeros(arr) {
	    return deepEqual(add(arr, Complex(1, 1)), add(multiply(arr, 0), Complex(1, 1)));
	  }
	});
	return pinv;
}

var eigs = {};

var complexEigs = {};

var hasRequiredComplexEigs;

function requireComplexEigs () {
	if (hasRequiredComplexEigs) return complexEigs;
	hasRequiredComplexEigs = 1;

	Object.defineProperty(complexEigs, "__esModule", {
	  value: true
	});
	complexEigs.createComplexEigs = createComplexEigs;
	var _object = requireObject$3();
	function createComplexEigs(_ref) {
	  let {
	    addScalar,
	    subtract,
	    flatten,
	    multiply,
	    multiplyScalar,
	    divideScalar,
	    sqrt,
	    abs,
	    bignumber,
	    diag,
	    size,
	    reshape,
	    inv,
	    qr,
	    usolve,
	    usolveAll,
	    equal,
	    complex,
	    larger,
	    smaller,
	    matrixFromColumns,
	    dot
	  } = _ref;
	  /**
	   * @param {number[][]} arr the matrix to find eigenvalues of
	   * @param {number} N size of the matrix
	   * @param {number|BigNumber} prec precision, anything lower will be considered zero
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @param {boolean} findVectors should we find eigenvectors?
	   *
	   * @returns {{ values: number[], vectors: number[][] }}
	   */
	  function complexEigs(arr, N, prec, type) {
	    let findVectors = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
	    // TODO check if any row/col are zero except the diagonal

	    // make sure corresponding rows and columns have similar magnitude
	    // important because of numerical stability
	    // MODIFIES arr by side effect!
	    const R = balance(arr, N, prec, type, findVectors);

	    // R is the row transformation matrix
	    // arr = A' = R A R^-1, A is the original matrix
	    // (if findVectors is false, R is undefined)
	    // (And so to return to original matrix: A = R^-1 arr R)

	    // TODO if magnitudes of elements vary over many orders,
	    // move greatest elements to the top left corner

	    // using similarity transformations, reduce the matrix
	    // to Hessenberg form (upper triangular plus one subdiagonal row)
	    // updates the transformation matrix R with new row operationsq
	    // MODIFIES arr by side effect!
	    reduceToHessenberg(arr, N, prec, type, findVectors, R);
	    // still true that original A = R^-1 arr R)

	    // find eigenvalues
	    const {
	      values,
	      C
	    } = iterateUntilTriangular(arr, N, prec, type, findVectors);

	    // values is the list of eigenvalues, C is the column
	    // transformation matrix that transforms arr, the hessenberg
	    // matrix, to upper triangular
	    // (So U = C^-1 arr C and the relationship between current arr
	    // and original A is unchanged.)

	    if (findVectors) {
	      const eigenvectors = findEigenvectors(arr, N, C, R, values, prec, type);
	      return {
	        values,
	        eigenvectors
	      };
	    }
	    return {
	      values
	    };
	  }

	  /**
	   * @param {number[][]} arr
	   * @param {number} N
	   * @param {number} prec
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @returns {number[][]}
	   */
	  function balance(arr, N, prec, type, findVectors) {
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';
	    const realzero = big ? bignumber(0) : 0;
	    const one = big ? bignumber(1) : cplx ? complex(1) : 1;
	    const realone = big ? bignumber(1) : 1;

	    // base of the floating-point arithmetic
	    const radix = big ? bignumber(10) : 2;
	    const radixSq = multiplyScalar(radix, radix);

	    // the diagonal transformation matrix R
	    let Rdiag;
	    if (findVectors) {
	      Rdiag = Array(N).fill(one);
	    }

	    // this isn't the only time we loop thru the matrix...
	    let last = false;
	    while (!last) {
	      // ...haha I'm joking! unless...
	      last = true;
	      for (let i = 0; i < N; i++) {
	        // compute the taxicab norm of i-th column and row
	        // TODO optimize for complex numbers
	        let colNorm = realzero;
	        let rowNorm = realzero;
	        for (let j = 0; j < N; j++) {
	          if (i === j) continue;
	          colNorm = addScalar(colNorm, abs(arr[j][i]));
	          rowNorm = addScalar(rowNorm, abs(arr[i][j]));
	        }
	        if (!equal(colNorm, 0) && !equal(rowNorm, 0)) {
	          // find integer power closest to balancing the matrix
	          // (we want to scale only by integer powers of radix,
	          // so that we don't lose any precision due to round-off)

	          let f = realone;
	          let c = colNorm;
	          const rowDivRadix = divideScalar(rowNorm, radix);
	          const rowMulRadix = multiplyScalar(rowNorm, radix);
	          while (smaller(c, rowDivRadix)) {
	            c = multiplyScalar(c, radixSq);
	            f = multiplyScalar(f, radix);
	          }
	          while (larger(c, rowMulRadix)) {
	            c = divideScalar(c, radixSq);
	            f = divideScalar(f, radix);
	          }

	          // check whether balancing is needed
	          // condition = (c + rowNorm) / f < 0.95 * (colNorm + rowNorm)
	          const condition = smaller(divideScalar(addScalar(c, rowNorm), f), multiplyScalar(addScalar(colNorm, rowNorm), 0.95));

	          // apply balancing similarity transformation
	          if (condition) {
	            // we should loop once again to check whether
	            // another rebalancing is needed
	            last = false;
	            const g = divideScalar(1, f);
	            for (let j = 0; j < N; j++) {
	              if (i === j) {
	                continue;
	              }
	              arr[i][j] = multiplyScalar(arr[i][j], g);
	              arr[j][i] = multiplyScalar(arr[j][i], f);
	            }

	            // keep track of transformations
	            if (findVectors) {
	              Rdiag[i] = multiplyScalar(Rdiag[i], g);
	            }
	          }
	        }
	      }
	    }

	    // return the diagonal row transformation matrix
	    return findVectors ? diag(Rdiag) : null;
	  }

	  /**
	   * @param {number[][]} arr
	   * @param {number} N
	   * @param {number} prec
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @param {boolean} findVectors
	   * @param {number[][]} R the row transformation matrix that will be modified
	   */
	  function reduceToHessenberg(arr, N, prec, type, findVectors, R) {
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';
	    const zero = big ? bignumber(0) : cplx ? complex(0) : 0;
	    if (big) {
	      prec = bignumber(prec);
	    }
	    for (let i = 0; i < N - 2; i++) {
	      // Find the largest subdiag element in the i-th col

	      let maxIndex = 0;
	      let max = zero;
	      for (let j = i + 1; j < N; j++) {
	        const el = arr[j][i];
	        if (smaller(abs(max), abs(el))) {
	          max = el;
	          maxIndex = j;
	        }
	      }

	      // This col is pivoted, no need to do anything
	      if (smaller(abs(max), prec)) {
	        continue;
	      }
	      if (maxIndex !== i + 1) {
	        // Interchange maxIndex-th and (i+1)-th row
	        const tmp1 = arr[maxIndex];
	        arr[maxIndex] = arr[i + 1];
	        arr[i + 1] = tmp1;

	        // Interchange maxIndex-th and (i+1)-th column
	        for (let j = 0; j < N; j++) {
	          const tmp2 = arr[j][maxIndex];
	          arr[j][maxIndex] = arr[j][i + 1];
	          arr[j][i + 1] = tmp2;
	        }

	        // keep track of transformations
	        if (findVectors) {
	          const tmp3 = R[maxIndex];
	          R[maxIndex] = R[i + 1];
	          R[i + 1] = tmp3;
	        }
	      }

	      // Reduce following rows and columns
	      for (let j = i + 2; j < N; j++) {
	        const n = divideScalar(arr[j][i], max);
	        if (n === 0) {
	          continue;
	        }

	        // from j-th row subtract n-times (i+1)th row
	        for (let k = 0; k < N; k++) {
	          arr[j][k] = subtract(arr[j][k], multiplyScalar(n, arr[i + 1][k]));
	        }

	        // to (i+1)th column add n-times j-th column
	        for (let k = 0; k < N; k++) {
	          arr[k][i + 1] = addScalar(arr[k][i + 1], multiplyScalar(n, arr[k][j]));
	        }

	        // keep track of transformations
	        if (findVectors) {
	          for (let k = 0; k < N; k++) {
	            R[j][k] = subtract(R[j][k], multiplyScalar(n, R[i + 1][k]));
	          }
	        }
	      }
	    }
	    return R;
	  }

	  /**
	   * @returns {{values: values, C: Matrix}}
	   * @see Press, Wiliams: Numerical recipes in Fortran 77
	   * @see https://en.wikipedia.org/wiki/QR_algorithm
	   */
	  function iterateUntilTriangular(A, N, prec, type, findVectors) {
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';
	    const one = big ? bignumber(1) : cplx ? complex(1) : 1;
	    if (big) {
	      prec = bignumber(prec);
	    }

	    // The Francis Algorithm
	    // The core idea of this algorithm is that doing successive
	    // A' = QtAQ transformations will eventually converge to block-
	    // upper-triangular with diagonal blocks either 1x1 or 2x2.
	    // The Q here is the one from the QR decomposition, A = QR.
	    // Since the eigenvalues of a block-upper-triangular matrix are
	    // the eigenvalues of its diagonal blocks and we know how to find
	    // eigenvalues of a 2x2 matrix, we know the eigenvalues of A.

	    let arr = (0, _object.clone)(A);

	    // the list of converged eigenvalues
	    const lambdas = [];

	    // size of arr, which will get smaller as eigenvalues converge
	    let n = N;

	    // the diagonal of the block-diagonal matrix that turns
	    // converged 2x2 matrices into upper triangular matrices
	    const Sdiag = [];

	    // N×N matrix describing the overall transformation done during the QR algorithm
	    let Qtotal = findVectors ? diag(Array(N).fill(one)) : undefined;

	    // nxn matrix describing the QR transformations done since last convergence
	    let Qpartial = findVectors ? diag(Array(n).fill(one)) : undefined;

	    // last eigenvalue converged before this many steps
	    let lastConvergenceBefore = 0;
	    while (lastConvergenceBefore <= 100) {
	      lastConvergenceBefore += 1;

	      // TODO if the convergence is slow, do something clever

	      // Perform the factorization

	      const k = arr[n - 1][n - 1]; // TODO this is apparently a somewhat
	      // old-fashioned choice; ideally set close to an eigenvalue, or
	      // perhaps better yet switch to the implicit QR version that is sometimes
	      // specifically called the "Francis algorithm" that is alluded to
	      // in the following TODO. (Or perhaps we switch to an independently
	      // optimized third-party package for the linear algebra operations...)

	      for (let i = 0; i < n; i++) {
	        arr[i][i] = subtract(arr[i][i], k);
	      }

	      // TODO do an implicit QR transformation
	      const {
	        Q,
	        R
	      } = qr(arr);
	      arr = multiply(R, Q);
	      for (let i = 0; i < n; i++) {
	        arr[i][i] = addScalar(arr[i][i], k);
	      }

	      // keep track of transformations
	      if (findVectors) {
	        Qpartial = multiply(Qpartial, Q);
	      }

	      // The rightmost diagonal element converged to an eigenvalue
	      if (n === 1 || smaller(abs(arr[n - 1][n - 2]), prec)) {
	        lastConvergenceBefore = 0;
	        lambdas.push(arr[n - 1][n - 1]);

	        // keep track of transformations
	        if (findVectors) {
	          Sdiag.unshift([[1]]);
	          inflateMatrix(Qpartial, N);
	          Qtotal = multiply(Qtotal, Qpartial);
	          if (n > 1) {
	            Qpartial = diag(Array(n - 1).fill(one));
	          }
	        }

	        // reduce the matrix size
	        n -= 1;
	        arr.pop();
	        for (let i = 0; i < n; i++) {
	          arr[i].pop();
	        }

	        // The rightmost diagonal 2x2 block converged
	      } else if (n === 2 || smaller(abs(arr[n - 2][n - 3]), prec)) {
	        lastConvergenceBefore = 0;
	        const ll = eigenvalues2x2(arr[n - 2][n - 2], arr[n - 2][n - 1], arr[n - 1][n - 2], arr[n - 1][n - 1]);
	        lambdas.push(...ll);

	        // keep track of transformations
	        if (findVectors) {
	          Sdiag.unshift(jordanBase2x2(arr[n - 2][n - 2], arr[n - 2][n - 1], arr[n - 1][n - 2], arr[n - 1][n - 1], ll[0], ll[1], prec, type));
	          inflateMatrix(Qpartial, N);
	          Qtotal = multiply(Qtotal, Qpartial);
	          if (n > 2) {
	            Qpartial = diag(Array(n - 2).fill(one));
	          }
	        }

	        // reduce the matrix size
	        n -= 2;
	        arr.pop();
	        arr.pop();
	        for (let i = 0; i < n; i++) {
	          arr[i].pop();
	          arr[i].pop();
	        }
	      }
	      if (n === 0) {
	        break;
	      }
	    }

	    // standard sorting
	    lambdas.sort((a, b) => +subtract(abs(a), abs(b)));

	    // the algorithm didn't converge
	    if (lastConvergenceBefore > 100) {
	      const err = Error('The eigenvalues failed to converge. Only found these eigenvalues: ' + lambdas.join(', '));
	      err.values = lambdas;
	      err.vectors = [];
	      throw err;
	    }

	    // combine the overall QR transformation Qtotal with the subsequent
	    // transformation S that turns the diagonal 2x2 blocks to upper triangular
	    const C = findVectors ? multiply(Qtotal, blockDiag(Sdiag, N)) : undefined;
	    return {
	      values: lambdas,
	      C
	    };
	  }

	  /**
	   * @param {Matrix} A hessenberg-form matrix
	   * @param {number} N size of A
	   * @param {Matrix} C column transformation matrix that turns A into upper triangular
	   * @param {Matrix} R similarity that turns original matrix into A
	   * @param {number[]} values array of eigenvalues of A
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @returns {number[][]} eigenvalues
	   */
	  function findEigenvectors(A, N, C, R, values, prec, type) {
	    const Cinv = inv(C);
	    const U = multiply(Cinv, A, C);
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';
	    const zero = big ? bignumber(0) : cplx ? complex(0) : 0;
	    const one = big ? bignumber(1) : cplx ? complex(1) : 1;

	    // turn values into a kind of "multiset"
	    // this way it is easier to find eigenvectors
	    const uniqueValues = [];
	    const multiplicities = [];
	    for (const lambda of values) {
	      const i = indexOf(uniqueValues, lambda, equal);
	      if (i === -1) {
	        uniqueValues.push(lambda);
	        multiplicities.push(1);
	      } else {
	        multiplicities[i] += 1;
	      }
	    }

	    // find eigenvectors by solving U − lambdaE = 0
	    // TODO replace with an iterative eigenvector algorithm
	    // (this one might fail for imprecise eigenvalues)

	    const vectors = [];
	    const len = uniqueValues.length;
	    const b = Array(N).fill(zero);
	    const E = diag(Array(N).fill(one));
	    for (let i = 0; i < len; i++) {
	      const lambda = uniqueValues[i];
	      const S = subtract(U, multiply(lambda, E)); // the characteristic matrix

	      let solutions = usolveAll(S, b);
	      solutions.shift(); // ignore the null vector

	      // looks like we missed something, try inverse iteration
	      // But if that fails, just presume that the original matrix truly
	      // was defective.
	      while (solutions.length < multiplicities[i]) {
	        const approxVec = inverseIterate(S, N, solutions, prec, type);
	        if (approxVec === null) {
	          break;
	        } // no more vectors were found
	        solutions.push(approxVec);
	      }

	      // Transform back into original array coordinates
	      const correction = multiply(inv(R), C);
	      solutions = solutions.map(v => multiply(correction, v));
	      vectors.push(...solutions.map(v => ({
	        value: lambda,
	        vector: flatten(v)
	      })));
	    }
	    return vectors;
	  }

	  /**
	   * Compute the eigenvalues of an 2x2 matrix
	   * @return {[number,number]}
	   */
	  function eigenvalues2x2(a, b, c, d) {
	    // lambda_+- = 1/2 trA +- 1/2 sqrt( tr^2 A - 4 detA )
	    const trA = addScalar(a, d);
	    const detA = subtract(multiplyScalar(a, d), multiplyScalar(b, c));
	    const x = multiplyScalar(trA, 0.5);
	    const y = multiplyScalar(sqrt(subtract(multiplyScalar(trA, trA), multiplyScalar(4, detA))), 0.5);
	    return [addScalar(x, y), subtract(x, y)];
	  }

	  /**
	   * For an 2x2 matrix compute the transformation matrix S,
	   * so that SAS^-1 is an upper triangular matrix
	   * @return {[[number,number],[number,number]]}
	   * @see https://math.berkeley.edu/~ogus/old/Math_54-05/webfoils/jordan.pdf
	   * @see http://people.math.harvard.edu/~knill/teaching/math21b2004/exhibits/2dmatrices/index.html
	   */
	  function jordanBase2x2(a, b, c, d, l1, l2, prec, type) {
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';
	    const zero = big ? bignumber(0) : cplx ? complex(0) : 0;
	    const one = big ? bignumber(1) : cplx ? complex(1) : 1;

	    // matrix is already upper triangular
	    // return an identity matrix
	    if (smaller(abs(c), prec)) {
	      return [[one, zero], [zero, one]];
	    }

	    // matrix is diagonalizable
	    // return its eigenvectors as columns
	    if (larger(abs(subtract(l1, l2)), prec)) {
	      return [[subtract(l1, d), subtract(l2, d)], [c, c]];
	    }

	    // matrix is not diagonalizable
	    // compute diagonal elements of N = A - lambdaI
	    const na = subtract(a, l1);
	    const nd = subtract(d, l1);

	    // col(N,2) = 0  implies  S = ( col(N,1), e_1 )
	    // col(N,2) != 0 implies  S = ( col(N,2), e_2 )

	    if (smaller(abs(b), prec) && smaller(abs(nd), prec)) {
	      return [[na, one], [c, zero]];
	    } else {
	      return [[b, zero], [nd, one]];
	    }
	  }

	  /**
	   * Enlarge the matrix from nxn to NxN, setting the new
	   * elements to 1 on diagonal and 0 elsewhere
	   */
	  function inflateMatrix(arr, N) {
	    // add columns
	    for (let i = 0; i < arr.length; i++) {
	      arr[i].push(...Array(N - arr[i].length).fill(0));
	    }

	    // add rows
	    for (let i = arr.length; i < N; i++) {
	      arr.push(Array(N).fill(0));
	      arr[i][i] = 1;
	    }
	    return arr;
	  }

	  /**
	   * Create a block-diagonal matrix with the given square matrices on the diagonal
	   * @param {Matrix[] | number[][][]} arr array of matrices to be placed on the diagonal
	   * @param {number} N the size of the resulting matrix
	   */
	  function blockDiag(arr, N) {
	    const M = [];
	    for (let i = 0; i < N; i++) {
	      M[i] = Array(N).fill(0);
	    }
	    let I = 0;
	    for (const sub of arr) {
	      const n = sub.length;
	      for (let i = 0; i < n; i++) {
	        for (let j = 0; j < n; j++) {
	          M[I + i][I + j] = sub[i][j];
	        }
	      }
	      I += n;
	    }
	    return M;
	  }

	  /**
	   * Finds the index of an element in an array using a custom equality function
	   * @template T
	   * @param {Array<T>} arr array in which to search
	   * @param {T} el the element to find
	   * @param {function(T, T): boolean} fn the equality function, first argument is an element of `arr`, the second is always `el`
	   * @returns {number} the index of `el`, or -1 when it's not in `arr`
	   */
	  function indexOf(arr, el, fn) {
	    for (let i = 0; i < arr.length; i++) {
	      if (fn(arr[i], el)) {
	        return i;
	      }
	    }
	    return -1;
	  }

	  /**
	   * Provided a near-singular upper-triangular matrix A and a list of vectors,
	   * finds an eigenvector of A with the smallest eigenvalue, which is orthogonal
	   * to each vector in the list
	   * @template T
	   * @param {T[][]} A near-singular square matrix
	   * @param {number} N dimension
	   * @param {T[][]} orthog list of vectors
	   * @param {number} prec epsilon
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @return {T[] | null} eigenvector
	   *
	   * @see Numerical Recipes for Fortran 77 – 11.7 Eigenvalues or Eigenvectors by Inverse Iteration
	   */
	  function inverseIterate(A, N, orthog, prec, type) {
	    const largeNum = type === 'BigNumber' ? bignumber(1000) : 1000;
	    let b; // the vector

	    // you better choose a random vector before I count to five
	    let i = 0;
	    for (; i < 5; ++i) {
	      b = randomOrthogonalVector(N, orthog, type);
	      try {
	        b = usolve(A, b);
	      } catch (_unused) {
	        // That direction didn't work, likely because the original matrix
	        // was defective. But still make the full number of tries...
	        continue;
	      }
	      if (larger(norm(b), largeNum)) {
	        break;
	      }
	    }
	    if (i >= 5) {
	      return null; // couldn't find any orthogonal vector in the image
	    }

	    // you better converge before I count to ten
	    i = 0;
	    while (true) {
	      const c = usolve(A, b);
	      if (smaller(norm(orthogonalComplement(b, [c])), prec)) {
	        break;
	      }
	      if (++i >= 10) {
	        return null;
	      }
	      b = normalize(c);
	    }
	    return b;
	  }

	  /**
	   * Generates a random unit vector of dimension N, orthogonal to each vector in the list
	   * @template T
	   * @param {number} N dimension
	   * @param {T[][]} orthog list of vectors
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @returns {T[]} random vector
	   */
	  function randomOrthogonalVector(N, orthog, type) {
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';

	    // generate random vector with the correct type
	    let v = Array(N).fill(0).map(_ => 2 * Math.random() - 1);
	    if (big) {
	      v = v.map(n => bignumber(n));
	    }
	    if (cplx) {
	      v = v.map(n => complex(n));
	    }

	    // project to orthogonal complement
	    v = orthogonalComplement(v, orthog);

	    // normalize
	    return normalize(v, type);
	  }

	  /**
	   * Project vector v to the orthogonal complement of an array of vectors
	   */
	  function orthogonalComplement(v, orthog) {
	    const vectorShape = size(v);
	    for (let w of orthog) {
	      w = reshape(w, vectorShape); // make sure this is just a vector computation
	      // v := v − (w, v)/|w|^2 w
	      v = subtract(v, multiply(divideScalar(dot(w, v), dot(w, w)), w));
	    }
	    return v;
	  }

	  /**
	   * Calculate the norm of a vector.
	   * We can't use math.norm because factory can't handle circular dependency.
	   * Seriously, I'm really fed up with factory.
	   */
	  function norm(v) {
	    return abs(sqrt(dot(v, v)));
	  }

	  /**
	   * Normalize a vector
	   * @template T
	   * @param {T[]} v
	   * @param {'number'|'BigNumber'|'Complex'} type
	   * @returns {T[]} normalized vec
	   */
	  function normalize(v, type) {
	    const big = type === 'BigNumber';
	    const cplx = type === 'Complex';
	    const one = big ? bignumber(1) : cplx ? complex(1) : 1;
	    return multiply(divideScalar(one, norm(v)), v);
	  }
	  return complexEigs;
	}
	return complexEigs;
}

var realSymmetric = {};

var hasRequiredRealSymmetric;

function requireRealSymmetric () {
	if (hasRequiredRealSymmetric) return realSymmetric;
	hasRequiredRealSymmetric = 1;

	Object.defineProperty(realSymmetric, "__esModule", {
	  value: true
	});
	realSymmetric.createRealSymmetric = createRealSymmetric;
	var _object = requireObject$3();
	function createRealSymmetric(_ref) {
	  let {
	    config,
	    addScalar,
	    subtract,
	    abs,
	    atan,
	    cos,
	    sin,
	    multiplyScalar,
	    inv,
	    bignumber,
	    multiply,
	    add
	  } = _ref;
	  /**
	   * @param {number[] | BigNumber[]} arr
	   * @param {number} N
	   * @param {number} prec
	   * @param {'number' | 'BigNumber'} type
	   */
	  function main(arr, N) {
	    let prec = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : config.relTol;
	    let type = arguments.length > 3 ? arguments[3] : undefined;
	    let computeVectors = arguments.length > 4 ? arguments[4] : undefined;
	    if (type === 'number') {
	      return diag(arr, prec, computeVectors);
	    }
	    if (type === 'BigNumber') {
	      return diagBig(arr, prec, computeVectors);
	    }
	    throw TypeError('Unsupported data type: ' + type);
	  }

	  // diagonalization implementation for number (efficient)
	  function diag(x, precision, computeVectors) {
	    const N = x.length;
	    const e0 = Math.abs(precision / N);
	    let psi;
	    let Sij;
	    if (computeVectors) {
	      Sij = new Array(N);
	      // Sij is Identity Matrix
	      for (let i = 0; i < N; i++) {
	        Sij[i] = Array(N).fill(0);
	        Sij[i][i] = 1.0;
	      }
	    }
	    // initial error
	    let Vab = getAij(x);
	    while (Math.abs(Vab[1]) >= Math.abs(e0)) {
	      const i = Vab[0][0];
	      const j = Vab[0][1];
	      psi = getTheta(x[i][i], x[j][j], x[i][j]);
	      x = x1(x, psi, i, j);
	      if (computeVectors) Sij = Sij1(Sij, psi, i, j);
	      Vab = getAij(x);
	    }
	    const Ei = Array(N).fill(0); // eigenvalues
	    for (let i = 0; i < N; i++) {
	      Ei[i] = x[i][i];
	    }
	    return sorting((0, _object.clone)(Ei), Sij, computeVectors);
	  }

	  // diagonalization implementation for bigNumber
	  function diagBig(x, precision, computeVectors) {
	    const N = x.length;
	    const e0 = abs(precision / N);
	    let psi;
	    let Sij;
	    if (computeVectors) {
	      Sij = new Array(N);
	      // Sij is Identity Matrix
	      for (let i = 0; i < N; i++) {
	        Sij[i] = Array(N).fill(0);
	        Sij[i][i] = 1.0;
	      }
	    }
	    // initial error
	    let Vab = getAijBig(x);
	    while (abs(Vab[1]) >= abs(e0)) {
	      const i = Vab[0][0];
	      const j = Vab[0][1];
	      psi = getThetaBig(x[i][i], x[j][j], x[i][j]);
	      x = x1Big(x, psi, i, j);
	      if (computeVectors) Sij = Sij1Big(Sij, psi, i, j);
	      Vab = getAijBig(x);
	    }
	    const Ei = Array(N).fill(0); // eigenvalues
	    for (let i = 0; i < N; i++) {
	      Ei[i] = x[i][i];
	    }
	    // return [clone(Ei), clone(Sij)]
	    return sorting((0, _object.clone)(Ei), Sij, computeVectors);
	  }

	  // get angle
	  function getTheta(aii, ajj, aij) {
	    const denom = ajj - aii;
	    if (Math.abs(denom) <= config.relTol) {
	      return Math.PI / 4.0;
	    } else {
	      return 0.5 * Math.atan(2.0 * aij / (ajj - aii));
	    }
	  }

	  // get angle
	  function getThetaBig(aii, ajj, aij) {
	    const denom = subtract(ajj, aii);
	    if (abs(denom) <= config.relTol) {
	      return bignumber(-1).acos().div(4);
	    } else {
	      return multiplyScalar(0.5, atan(multiply(2.0, aij, inv(denom))));
	    }
	  }

	  // update eigvec
	  function Sij1(Sij, theta, i, j) {
	    const N = Sij.length;
	    const c = Math.cos(theta);
	    const s = Math.sin(theta);
	    const Ski = Array(N).fill(0);
	    const Skj = Array(N).fill(0);
	    for (let k = 0; k < N; k++) {
	      Ski[k] = c * Sij[k][i] - s * Sij[k][j];
	      Skj[k] = s * Sij[k][i] + c * Sij[k][j];
	    }
	    for (let k = 0; k < N; k++) {
	      Sij[k][i] = Ski[k];
	      Sij[k][j] = Skj[k];
	    }
	    return Sij;
	  }
	  // update eigvec for overlap
	  function Sij1Big(Sij, theta, i, j) {
	    const N = Sij.length;
	    const c = cos(theta);
	    const s = sin(theta);
	    const Ski = Array(N).fill(bignumber(0));
	    const Skj = Array(N).fill(bignumber(0));
	    for (let k = 0; k < N; k++) {
	      Ski[k] = subtract(multiplyScalar(c, Sij[k][i]), multiplyScalar(s, Sij[k][j]));
	      Skj[k] = addScalar(multiplyScalar(s, Sij[k][i]), multiplyScalar(c, Sij[k][j]));
	    }
	    for (let k = 0; k < N; k++) {
	      Sij[k][i] = Ski[k];
	      Sij[k][j] = Skj[k];
	    }
	    return Sij;
	  }

	  // update matrix
	  function x1Big(Hij, theta, i, j) {
	    const N = Hij.length;
	    const c = bignumber(cos(theta));
	    const s = bignumber(sin(theta));
	    const c2 = multiplyScalar(c, c);
	    const s2 = multiplyScalar(s, s);
	    const Aki = Array(N).fill(bignumber(0));
	    const Akj = Array(N).fill(bignumber(0));
	    // 2cs Hij
	    const csHij = multiply(bignumber(2), c, s, Hij[i][j]);
	    //  Aii
	    const Aii = addScalar(subtract(multiplyScalar(c2, Hij[i][i]), csHij), multiplyScalar(s2, Hij[j][j]));
	    const Ajj = add(multiplyScalar(s2, Hij[i][i]), csHij, multiplyScalar(c2, Hij[j][j]));
	    // 0  to i
	    for (let k = 0; k < N; k++) {
	      Aki[k] = subtract(multiplyScalar(c, Hij[i][k]), multiplyScalar(s, Hij[j][k]));
	      Akj[k] = addScalar(multiplyScalar(s, Hij[i][k]), multiplyScalar(c, Hij[j][k]));
	    }
	    // Modify Hij
	    Hij[i][i] = Aii;
	    Hij[j][j] = Ajj;
	    Hij[i][j] = bignumber(0);
	    Hij[j][i] = bignumber(0);
	    // 0  to i
	    for (let k = 0; k < N; k++) {
	      if (k !== i && k !== j) {
	        Hij[i][k] = Aki[k];
	        Hij[k][i] = Aki[k];
	        Hij[j][k] = Akj[k];
	        Hij[k][j] = Akj[k];
	      }
	    }
	    return Hij;
	  }

	  // update matrix
	  function x1(Hij, theta, i, j) {
	    const N = Hij.length;
	    const c = Math.cos(theta);
	    const s = Math.sin(theta);
	    const c2 = c * c;
	    const s2 = s * s;
	    const Aki = Array(N).fill(0);
	    const Akj = Array(N).fill(0);
	    //  Aii
	    const Aii = c2 * Hij[i][i] - 2 * c * s * Hij[i][j] + s2 * Hij[j][j];
	    const Ajj = s2 * Hij[i][i] + 2 * c * s * Hij[i][j] + c2 * Hij[j][j];
	    // 0  to i
	    for (let k = 0; k < N; k++) {
	      Aki[k] = c * Hij[i][k] - s * Hij[j][k];
	      Akj[k] = s * Hij[i][k] + c * Hij[j][k];
	    }
	    // Modify Hij
	    Hij[i][i] = Aii;
	    Hij[j][j] = Ajj;
	    Hij[i][j] = 0;
	    Hij[j][i] = 0;
	    // 0  to i
	    for (let k = 0; k < N; k++) {
	      if (k !== i && k !== j) {
	        Hij[i][k] = Aki[k];
	        Hij[k][i] = Aki[k];
	        Hij[j][k] = Akj[k];
	        Hij[k][j] = Akj[k];
	      }
	    }
	    return Hij;
	  }

	  // get max off-diagonal value from Upper Diagonal
	  function getAij(Mij) {
	    const N = Mij.length;
	    let maxMij = 0;
	    let maxIJ = [0, 1];
	    for (let i = 0; i < N; i++) {
	      for (let j = i + 1; j < N; j++) {
	        if (Math.abs(maxMij) < Math.abs(Mij[i][j])) {
	          maxMij = Math.abs(Mij[i][j]);
	          maxIJ = [i, j];
	        }
	      }
	    }
	    return [maxIJ, maxMij];
	  }

	  // get max off-diagonal value from Upper Diagonal
	  function getAijBig(Mij) {
	    const N = Mij.length;
	    let maxMij = 0;
	    let maxIJ = [0, 1];
	    for (let i = 0; i < N; i++) {
	      for (let j = i + 1; j < N; j++) {
	        if (abs(maxMij) < abs(Mij[i][j])) {
	          maxMij = abs(Mij[i][j]);
	          maxIJ = [i, j];
	        }
	      }
	    }
	    return [maxIJ, maxMij];
	  }

	  // sort results
	  function sorting(E, S, computeVectors) {
	    const N = E.length;
	    const values = Array(N);
	    let vecs;
	    if (computeVectors) {
	      vecs = Array(N);
	      for (let k = 0; k < N; k++) {
	        vecs[k] = Array(N);
	      }
	    }
	    for (let i = 0; i < N; i++) {
	      let minID = 0;
	      let minE = E[0];
	      for (let j = 0; j < E.length; j++) {
	        if (abs(E[j]) < abs(minE)) {
	          minID = j;
	          minE = E[minID];
	        }
	      }
	      values[i] = E.splice(minID, 1)[0];
	      if (computeVectors) {
	        for (let k = 0; k < N; k++) {
	          vecs[i][k] = S[k][minID];
	          S[k].splice(minID, 1);
	        }
	      }
	    }
	    if (!computeVectors) return {
	      values
	    };
	    const eigenvectors = vecs.map((vector, i) => ({
	      value: values[i],
	      vector
	    }));
	    return {
	      values,
	      eigenvectors
	    };
	  }
	  return main;
	}
	return realSymmetric;
}

var hasRequiredEigs;

function requireEigs () {
	if (hasRequiredEigs) return eigs;
	hasRequiredEigs = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(eigs, "__esModule", {
	  value: true
	});
	eigs.createEigs = void 0;
	var _extends2 = _interopRequireDefault(require_extends());
	var _factory = requireFactory$b();
	var _string = requireString$2();
	var _complexEigs = requireComplexEigs();
	var _realSymmetric = requireRealSymmetric();
	var _is = requireIs();
	const name = 'eigs';

	// The absolute state of math.js's dependency system:
	const dependencies = ['config', 'typed', 'matrix', 'addScalar', 'equal', 'subtract', 'abs', 'atan', 'cos', 'sin', 'multiplyScalar', 'divideScalar', 'inv', 'bignumber', 'multiply', 'add', 'larger', 'column', 'flatten', 'number', 'complex', 'sqrt', 'diag', 'size', 'reshape', 'qr', 'usolve', 'usolveAll', 'im', 're', 'smaller', 'matrixFromColumns', 'dot'];
	eigs.createEigs = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    config,
	    typed,
	    matrix,
	    addScalar,
	    subtract,
	    equal,
	    abs,
	    atan,
	    cos,
	    sin,
	    multiplyScalar,
	    divideScalar,
	    inv,
	    bignumber,
	    multiply,
	    add,
	    larger,
	    column,
	    flatten,
	    number,
	    complex,
	    sqrt,
	    diag,
	    size,
	    reshape,
	    qr,
	    usolve,
	    usolveAll,
	    im,
	    re,
	    smaller,
	    matrixFromColumns,
	    dot
	  } = _ref;
	  const doRealSymmetric = (0, _realSymmetric.createRealSymmetric)({
	    config,
	    addScalar,
	    subtract,
	    column,
	    flatten,
	    equal,
	    abs,
	    atan,
	    cos,
	    sin,
	    multiplyScalar,
	    inv,
	    bignumber,
	    complex,
	    multiply,
	    add
	  });
	  const doComplexEigs = (0, _complexEigs.createComplexEigs)({
	    config,
	    addScalar,
	    subtract,
	    multiply,
	    multiplyScalar,
	    flatten,
	    divideScalar,
	    sqrt,
	    abs,
	    bignumber,
	    diag,
	    size,
	    reshape,
	    qr,
	    inv,
	    usolve,
	    usolveAll,
	    equal,
	    complex,
	    larger,
	    smaller,
	    matrixFromColumns,
	    dot
	  });

	  /**
	   * Compute eigenvalues and optionally eigenvectors of a square matrix.
	   * The eigenvalues are sorted by their absolute value, ascending, and
	   * returned as a vector in the `values` property of the returned project.
	   * An eigenvalue with algebraic multiplicity k will be listed k times, so
	   * that the returned `values` vector always has length equal to the size
	   * of the input matrix.
	   *
	   * The `eigenvectors` property of the return value provides the eigenvectors.
	   * It is an array of plain objects: the `value` property of each gives the
	   * associated eigenvalue, and the `vector` property gives the eigenvector
	   * itself. Note that the same `value` property will occur as many times in
	   * the list provided by `eigenvectors` as the geometric multiplicity of
	   * that value.
	   *
	   * If the algorithm fails to converge, it will throw an error –
	   * in that case, however, you may still find useful information
	   * in `err.values` and `err.vectors`.
	   *
	   * Note that the 'precision' option does not directly specify the _accuracy_
	   * of the returned eigenvalues. Rather, it determines how small an entry
	   * of the iterative approximations to an upper triangular matrix must be
	   * in order to be considered zero. The actual accuracy of the returned
	   * eigenvalues may be greater or less than the precision, depending on the
	   * conditioning of the matrix and how far apart or close the actual
	   * eigenvalues are. Note that currently, relatively simple, "traditional"
	   * methods of eigenvalue computation are being used; this is not a modern,
	   * high-precision eigenvalue computation. That said, it should typically
	   * produce fairly reasonable results.
	   *
	   * Syntax:
	   *
	   *     math.eigs(x, [prec])
	   *     math.eigs(x, {options})
	   *
	   * Examples:
	   *
	   *     const { eigs, multiply, column, transpose, matrixFromColumns } = math
	   *     const H = [[5, 2.3], [2.3, 1]]
	   *     const ans = eigs(H) // returns {values: [E1,E2...sorted], eigenvectors: [{value: E1, vector: v2}, {value: e, vector: v2}, ...]
	   *     const E = ans.values
	   *     const V = ans.eigenvectors
	   *     multiply(H, V[0].vector)) // returns multiply(E[0], V[0].vector))
	   *     const U = matrixFromColumns(...V.map(obj => obj.vector))
	   *     const UTxHxU = multiply(transpose(U), H, U) // diagonalizes H if possible
	   *     E[0] == UTxHxU[0][0]  // returns true always
	   *
	   *     // Compute only approximate eigenvalues:
	   *     const {values} = eigs(H, {eigenvectors: false, precision: 1e-6})
	   *
	   * See also:
	   *
	   *     inv
	   *
	   * @param {Array | Matrix} x  Matrix to be diagonalized
	   *
	   * @param {number | BigNumber | OptsObject} [opts] Object with keys `precision`, defaulting to config.relTol, and `eigenvectors`, defaulting to true and specifying whether to compute eigenvectors. If just a number, specifies precision.
	   * @return {{values: Array|Matrix, eigenvectors?: Array<EVobj>}} Object containing an array of eigenvalues and an array of {value: number|BigNumber, vector: Array|Matrix} objects. The eigenvectors property is undefined if eigenvectors were not requested.
	   *
	   */
	  return typed('eigs', {
	    // The conversion to matrix in the first two implementations,
	    // just to convert back to an array right away in
	    // computeValuesAndVectors, is unfortunate, and should perhaps be
	    // streamlined. It is done because the Matrix object carries some
	    // type information about its entries, and so constructing the matrix
	    // is a roundabout way of doing type detection.
	    Array: function (x) {
	      return doEigs(matrix(x));
	    },
	    'Array, number|BigNumber': function (x, prec) {
	      return doEigs(matrix(x), {
	        precision: prec
	      });
	    },
	    'Array, Object'(x, opts) {
	      return doEigs(matrix(x), opts);
	    },
	    Matrix: function (mat) {
	      return doEigs(mat, {
	        matricize: true
	      });
	    },
	    'Matrix, number|BigNumber': function (mat, prec) {
	      return doEigs(mat, {
	        precision: prec,
	        matricize: true
	      });
	    },
	    'Matrix, Object': function (mat, opts) {
	      const useOpts = {
	        matricize: true
	      };
	      (0, _extends2.default)(useOpts, opts);
	      return doEigs(mat, useOpts);
	    }
	  });
	  function doEigs(mat) {
	    var _opts$precision;
	    let opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	    const computeVectors = 'eigenvectors' in opts ? opts.eigenvectors : true;
	    const prec = (_opts$precision = opts.precision) !== null && _opts$precision !== void 0 ? _opts$precision : config.relTol;
	    const result = computeValuesAndVectors(mat, prec, computeVectors);
	    if (opts.matricize) {
	      result.values = matrix(result.values);
	      if (computeVectors) {
	        result.eigenvectors = result.eigenvectors.map(_ref2 => {
	          let {
	            value,
	            vector
	          } = _ref2;
	          return {
	            value,
	            vector: matrix(vector)
	          };
	        });
	      }
	    }
	    if (computeVectors) {
	      Object.defineProperty(result, 'vectors', {
	        enumerable: false,
	        // to make sure that the eigenvectors can still be
	        // converted to string.
	        get: () => {
	          throw new Error('eigs(M).vectors replaced with eigs(M).eigenvectors');
	        }
	      });
	    }
	    return result;
	  }
	  function computeValuesAndVectors(mat, prec, computeVectors) {
	    const arr = mat.toArray(); // NOTE: arr is guaranteed to be unaliased
	    // and so safe to modify in place
	    const asize = mat.size();
	    if (asize.length !== 2 || asize[0] !== asize[1]) {
	      throw new RangeError(`Matrix must be square (size: ${(0, _string.format)(asize)})`);
	    }
	    const N = asize[0];
	    if (isReal(arr, N, prec)) {
	      coerceReal(arr, N); // modifies arr by side effect

	      if (isSymmetric(arr, N, prec)) {
	        const type = coerceTypes(mat, arr, N); // modifies arr by side effect
	        return doRealSymmetric(arr, N, prec, type, computeVectors);
	      }
	    }
	    const type = coerceTypes(mat, arr, N); // modifies arr by side effect
	    return doComplexEigs(arr, N, prec, type, computeVectors);
	  }

	  /** @return {boolean} */
	  function isSymmetric(arr, N, prec) {
	    for (let i = 0; i < N; i++) {
	      for (let j = i; j < N; j++) {
	        // TODO proper comparison of bignum and frac
	        if (larger(bignumber(abs(subtract(arr[i][j], arr[j][i]))), prec)) {
	          return false;
	        }
	      }
	    }
	    return true;
	  }

	  /** @return {boolean} */
	  function isReal(arr, N, prec) {
	    for (let i = 0; i < N; i++) {
	      for (let j = 0; j < N; j++) {
	        // TODO proper comparison of bignum and frac
	        if (larger(bignumber(abs(im(arr[i][j]))), prec)) {
	          return false;
	        }
	      }
	    }
	    return true;
	  }
	  function coerceReal(arr, N) {
	    for (let i = 0; i < N; i++) {
	      for (let j = 0; j < N; j++) {
	        arr[i][j] = re(arr[i][j]);
	      }
	    }
	  }

	  /** @return {'number' | 'BigNumber' | 'Complex'} */
	  function coerceTypes(mat, arr, N) {
	    /** @type {string} */
	    const type = mat.datatype();
	    if (type === 'number' || type === 'BigNumber' || type === 'Complex') {
	      return type;
	    }
	    let hasNumber = false;
	    let hasBig = false;
	    let hasComplex = false;
	    for (let i = 0; i < N; i++) {
	      for (let j = 0; j < N; j++) {
	        const el = arr[i][j];
	        if ((0, _is.isNumber)(el) || (0, _is.isFraction)(el)) {
	          hasNumber = true;
	        } else if ((0, _is.isBigNumber)(el)) {
	          hasBig = true;
	        } else if ((0, _is.isComplex)(el)) {
	          hasComplex = true;
	        } else {
	          throw TypeError('Unsupported type in Matrix: ' + (0, _is.typeOf)(el));
	        }
	      }
	    }
	    if (hasBig && hasComplex) {
	      console.warn('Complex BigNumbers not supported, this operation will lose precission.');
	    }
	    if (hasComplex) {
	      for (let i = 0; i < N; i++) {
	        for (let j = 0; j < N; j++) {
	          arr[i][j] = complex(arr[i][j]);
	        }
	      }
	      return 'Complex';
	    }
	    if (hasBig) {
	      for (let i = 0; i < N; i++) {
	        for (let j = 0; j < N; j++) {
	          arr[i][j] = bignumber(arr[i][j]);
	        }
	      }
	      return 'BigNumber';
	    }
	    if (hasNumber) {
	      for (let i = 0; i < N; i++) {
	        for (let j = 0; j < N; j++) {
	          arr[i][j] = number(arr[i][j]);
	        }
	      }
	      return 'number';
	    } else {
	      throw TypeError('Matrix contains unsupported types only.');
	    }
	  }
	});
	return eigs;
}

var expm = {};

var hasRequiredExpm;

function requireExpm () {
	if (hasRequiredExpm) return expm;
	hasRequiredExpm = 1;

	Object.defineProperty(expm, "__esModule", {
	  value: true
	});
	expm.createExpm = void 0;
	var _is = requireIs();
	var _string = requireString$2();
	var _factory = requireFactory$b();
	const name = 'expm';
	const dependencies = ['typed', 'abs', 'add', 'identity', 'inv', 'multiply'];
	expm.createExpm = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    abs,
	    add,
	    identity,
	    inv,
	    multiply
	  } = _ref;
	  /**
	   * Compute the matrix exponential, expm(A) = e^A. The matrix must be square.
	   * Not to be confused with exp(a), which performs element-wise
	   * exponentiation.
	   *
	   * The exponential is calculated using the Padé approximant with scaling and
	   * squaring; see "Nineteen Dubious Ways to Compute the Exponential of a
	   * Matrix," by Moler and Van Loan.
	   *
	   * Syntax:
	   *
	   *     math.expm(x)
	   *
	   * Examples:
	   *
	   *     const A = [[0,2],[0,0]]
	   *     math.expm(A)        // returns [[1,2],[0,1]]
	   *
	   * See also:
	   *
	   *     exp
	   *
	   * @param {Matrix} x  A square Matrix
	   * @return {Matrix}   The exponential of x
	   */
	  return typed(name, {
	    Matrix: function (A) {
	      // Check matrix size
	      const size = A.size();
	      if (size.length !== 2 || size[0] !== size[1]) {
	        throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	      }
	      const n = size[0];

	      // Desired accuracy of the approximant (The actual accuracy
	      // will be affected by round-off error)
	      const eps = 1e-15;

	      // The Padé approximant is not so accurate when the values of A
	      // are "large", so scale A by powers of two. Then compute the
	      // exponential, and square the result repeatedly according to
	      // the identity e^A = (e^(A/m))^m

	      // Compute infinity-norm of A, ||A||, to see how "big" it is
	      const infNorm = infinityNorm(A);

	      // Find the optimal scaling factor and number of terms in the
	      // Padé approximant to reach the desired accuracy
	      const params = findParams(infNorm, eps);
	      const q = params.q;
	      const j = params.j;

	      // The Pade approximation to e^A is:
	      // Rqq(A) = Dqq(A) ^ -1 * Nqq(A)
	      // where
	      // Nqq(A) = sum(i=0, q, (2q-i)!p! / [ (2q)!i!(q-i)! ] A^i
	      // Dqq(A) = sum(i=0, q, (2q-i)!q! / [ (2q)!i!(q-i)! ] (-A)^i

	      // Scale A by 1 / 2^j
	      const Apos = multiply(A, Math.pow(2, -j));

	      // The i=0 term is just the identity matrix
	      let N = identity(n);
	      let D = identity(n);

	      // Initialization (i=0)
	      let factor = 1;

	      // Initialization (i=1)
	      let AposToI = Apos; // Cloning not necessary
	      let alternate = -1;
	      for (let i = 1; i <= q; i++) {
	        if (i > 1) {
	          AposToI = multiply(AposToI, Apos);
	          alternate = -alternate;
	        }
	        factor = factor * (q - i + 1) / ((2 * q - i + 1) * i);
	        N = add(N, multiply(factor, AposToI));
	        D = add(D, multiply(factor * alternate, AposToI));
	      }
	      let R = multiply(inv(D), N);

	      // Square j times
	      for (let i = 0; i < j; i++) {
	        R = multiply(R, R);
	      }
	      return (0, _is.isSparseMatrix)(A) ? A.createSparseMatrix(R) : R;
	    }
	  });
	  function infinityNorm(A) {
	    const n = A.size()[0];
	    let infNorm = 0;
	    for (let i = 0; i < n; i++) {
	      let rowSum = 0;
	      for (let j = 0; j < n; j++) {
	        rowSum += abs(A.get([i, j]));
	      }
	      infNorm = Math.max(rowSum, infNorm);
	    }
	    return infNorm;
	  }

	  /**
	   * Find the best parameters for the Pade approximant given
	   * the matrix norm and desired accuracy. Returns the first acceptable
	   * combination in order of increasing computational load.
	   */
	  function findParams(infNorm, eps) {
	    const maxSearchSize = 30;
	    for (let k = 0; k < maxSearchSize; k++) {
	      for (let q = 0; q <= k; q++) {
	        const j = k - q;
	        if (errorEstimate(infNorm, q, j) < eps) {
	          return {
	            q,
	            j
	          };
	        }
	      }
	    }
	    throw new Error('Could not find acceptable parameters to compute the matrix exponential (try increasing maxSearchSize in expm.js)');
	  }

	  /**
	   * Returns the estimated error of the Pade approximant for the given
	   * parameters.
	   */
	  function errorEstimate(infNorm, q, j) {
	    let qfac = 1;
	    for (let i = 2; i <= q; i++) {
	      qfac *= i;
	    }
	    let twoqfac = qfac;
	    for (let i = q + 1; i <= 2 * q; i++) {
	      twoqfac *= i;
	    }
	    const twoqp1fac = twoqfac * (2 * q + 1);
	    return 8.0 * Math.pow(infNorm / Math.pow(2, j), 2 * q) * qfac * qfac / (twoqfac * twoqp1fac);
	  }
	});
	return expm;
}

var sqrtm = {};

var hasRequiredSqrtm;

function requireSqrtm () {
	if (hasRequiredSqrtm) return sqrtm;
	hasRequiredSqrtm = 1;

	Object.defineProperty(sqrtm, "__esModule", {
	  value: true
	});
	sqrtm.createSqrtm = void 0;
	var _is = requireIs();
	var _string = requireString$2();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'sqrtm';
	const dependencies = ['typed', 'abs', 'add', 'multiply', 'map', 'sqrt', 'subtract', 'inv', 'size', 'max', 'identity'];
	sqrtm.createSqrtm = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    abs,
	    add,
	    multiply,
	    map,
	    sqrt,
	    subtract,
	    inv,
	    size,
	    max,
	    identity
	  } = _ref;
	  const _maxIterations = 1e3;
	  const _tolerance = 1e-6;

	  /**
	   * Calculate the principal square root matrix using the Denman–Beavers iterative method
	   *
	   * https://en.wikipedia.org/wiki/Square_root_of_a_matrix#By_Denman–Beavers_iteration
	   *
	   * @param  {Array | Matrix} A   The square matrix `A`
	   * @return {Array | Matrix}     The principal square root of matrix `A`
	   * @private
	   */
	  function _denmanBeavers(A) {
	    let error;
	    let iterations = 0;
	    let Y = A;
	    let Z = identity(size(A));
	    do {
	      const Yk = Y;
	      Y = multiply(0.5, add(Yk, inv(Z)));
	      Z = multiply(0.5, add(Z, inv(Yk)));
	      error = max(abs(subtract(Y, Yk)));
	      if (error > _tolerance && ++iterations > _maxIterations) {
	        throw new Error('computing square root of matrix: iterative method could not converge');
	      }
	    } while (error > _tolerance);
	    return Y;
	  }

	  /**
	   * Calculate the principal square root of a square matrix.
	   * The principal square root matrix `X` of another matrix `A` is such that `X * X = A`.
	   *
	   * https://en.wikipedia.org/wiki/Square_root_of_a_matrix
	   *
	   * Syntax:
	   *
	   *     math.sqrtm(A)
	   *
	   * Examples:
	   *
	   *     math.sqrtm([[33, 24], [48, 57]]) // returns [[5, 2], [4, 7]]
	   *
	   * See also:
	   *
	   *     sqrt, pow
	   *
	   * @param  {Array | Matrix} A   The square matrix `A`
	   * @return {Array | Matrix}     The principal square root of matrix `A`
	   */
	  return typed(name, {
	    'Array | Matrix': function (A) {
	      const size = (0, _is.isMatrix)(A) ? A.size() : (0, _array.arraySize)(A);
	      switch (size.length) {
	        case 1:
	          // Single element Array | Matrix
	          if (size[0] === 1) {
	            return map(A, sqrt);
	          } else {
	            throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	          }
	        case 2:
	          {
	            // Two-dimensional Array | Matrix
	            const rows = size[0];
	            const cols = size[1];
	            if (rows === cols) {
	              return _denmanBeavers(A);
	            } else {
	              throw new RangeError('Matrix must be square ' + '(size: ' + (0, _string.format)(size) + ')');
	            }
	          }
	        default:
	          // Multi dimensional array
	          throw new RangeError('Matrix must be at most two dimensional ' + '(size: ' + (0, _string.format)(size) + ')');
	      }
	    }
	  });
	});
	return sqrtm;
}

var sylvester = {};

var hasRequiredSylvester;

function requireSylvester () {
	if (hasRequiredSylvester) return sylvester;
	hasRequiredSylvester = 1;

	Object.defineProperty(sylvester, "__esModule", {
	  value: true
	});
	sylvester.createSylvester = void 0;
	var _factory = requireFactory$b();
	const name = 'sylvester';
	const dependencies = ['typed', 'schur', 'matrixFromColumns', 'matrix', 'multiply', 'range', 'concat', 'transpose', 'index', 'subset', 'add', 'subtract', 'identity', 'lusolve', 'abs'];
	sylvester.createSylvester = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    schur,
	    matrixFromColumns,
	    matrix,
	    multiply,
	    range,
	    concat,
	    transpose,
	    index,
	    subset,
	    add,
	    subtract,
	    identity,
	    lusolve,
	    abs,
	    config
	  } = _ref;
	  /**
	   *
	   * Solves the real-valued Sylvester equation AX+XB=C for X, where A, B and C are
	   * matrices of appropriate dimensions, being A and B squared. Notice that other
	   * equivalent definitions for the Sylvester equation exist and this function
	   * assumes the one presented in the original publication of the the Bartels-
	   * Stewart algorithm, which is implemented by this function.
	   * https://en.wikipedia.org/wiki/Sylvester_equation
	   *
	   * Syntax:
	   *
	   *     math.sylvester(A, B, C)
	   *
	   * Examples:
	   *
	   *     const A = [[-1, -2], [1, 1]]
	   *     const B = [[2, -1], [1, -2]]
	   *     const C = [[-3, 2], [3, 0]]
	   *     math.sylvester(A, B, C)      // returns DenseMatrix [[-0.25, 0.25], [1.5, -1.25]]
	   *
	   * See also:
	   *
	   *     schur, lyap
	   *
	   * @param {Matrix | Array} A  Matrix A
	   * @param {Matrix | Array} B  Matrix B
	   * @param {Matrix | Array} C  Matrix C
	   * @return {Matrix | Array}   Matrix X, solving the Sylvester equation
	   */
	  return typed(name, {
	    'Matrix, Matrix, Matrix': _sylvester,
	    'Array, Matrix, Matrix': function (A, B, C) {
	      return _sylvester(matrix(A), B, C);
	    },
	    'Array, Array, Matrix': function (A, B, C) {
	      return _sylvester(matrix(A), matrix(B), C);
	    },
	    'Array, Matrix, Array': function (A, B, C) {
	      return _sylvester(matrix(A), B, matrix(C));
	    },
	    'Matrix, Array, Matrix': function (A, B, C) {
	      return _sylvester(A, matrix(B), C);
	    },
	    'Matrix, Array, Array': function (A, B, C) {
	      return _sylvester(A, matrix(B), matrix(C));
	    },
	    'Matrix, Matrix, Array': function (A, B, C) {
	      return _sylvester(A, B, matrix(C));
	    },
	    'Array, Array, Array': function (A, B, C) {
	      return _sylvester(matrix(A), matrix(B), matrix(C)).toArray();
	    }
	  });
	  function _sylvester(A, B, C) {
	    const n = B.size()[0];
	    const m = A.size()[0];
	    const sA = schur(A);
	    const F = sA.T;
	    const U = sA.U;
	    const sB = schur(multiply(-1, B));
	    const G = sB.T;
	    const V = sB.U;
	    const D = multiply(multiply(transpose(U), C), V);
	    const all = range(0, m);
	    const y = [];
	    const hc = (a, b) => concat(a, b, 1);
	    const vc = (a, b) => concat(a, b, 0);
	    for (let k = 0; k < n; k++) {
	      if (k < n - 1 && abs(subset(G, index(k + 1, k))) > 1e-5) {
	        let RHS = vc(subset(D, index(all, [k])), subset(D, index(all, [k + 1])));
	        for (let j = 0; j < k; j++) {
	          RHS = add(RHS, vc(multiply(y[j], subset(G, index(j, k))), multiply(y[j], subset(G, index(j, k + 1)))));
	        }
	        const gkk = multiply(identity(m), multiply(-1, subset(G, index(k, k))));
	        const gmk = multiply(identity(m), multiply(-1, subset(G, index(k + 1, k))));
	        const gkm = multiply(identity(m), multiply(-1, subset(G, index(k, k + 1))));
	        const gmm = multiply(identity(m), multiply(-1, subset(G, index(k + 1, k + 1))));
	        const LHS = vc(hc(add(F, gkk), gmk), hc(gkm, add(F, gmm)));
	        const yAux = lusolve(LHS, RHS);
	        y[k] = yAux.subset(index(range(0, m), [0]));
	        y[k + 1] = yAux.subset(index(range(m, 2 * m), [0]));
	        k++;
	      } else {
	        let RHS = subset(D, index(all, [k]));
	        for (let j = 0; j < k; j++) {
	          RHS = add(RHS, multiply(y[j], subset(G, index(j, k))));
	        }
	        const gkk = subset(G, index(k, k));
	        const LHS = subtract(F, multiply(gkk, identity(m)));
	        y[k] = lusolve(LHS, RHS);
	      }
	    }
	    const Y = matrix(matrixFromColumns(...y));
	    const X = multiply(U, multiply(Y, transpose(V)));
	    return X;
	  }
	});
	return sylvester;
}

var schur = {};

var hasRequiredSchur;

function requireSchur () {
	if (hasRequiredSchur) return schur;
	hasRequiredSchur = 1;

	Object.defineProperty(schur, "__esModule", {
	  value: true
	});
	schur.createSchur = void 0;
	var _factory = requireFactory$b();
	const name = 'schur';
	const dependencies = ['typed', 'matrix', 'identity', 'multiply', 'qr', 'norm', 'subtract'];
	schur.createSchur = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    identity,
	    multiply,
	    qr,
	    norm,
	    subtract
	  } = _ref;
	  /**
	   *
	   * Performs a real Schur decomposition of the real matrix A = UTU' where U is orthogonal
	   * and T is upper quasi-triangular.
	   * https://en.wikipedia.org/wiki/Schur_decomposition
	   *
	   * Syntax:
	   *
	   *     math.schur(A)
	   *
	   * Examples:
	   *
	   *     const A = [[1, 0], [-4, 3]]
	   *     math.schur(A) // returns {T: [[3, 4], [0, 1]], R: [[0, 1], [-1, 0]]}
	   *
	   * See also:
	   *
	   *     sylvester, lyap, qr
	   *
	   * @param {Array | Matrix} A  Matrix A
	   * @return {{U: Array | Matrix, T: Array | Matrix}} Object containing both matrix U and T of the Schur Decomposition A=UTU'
	   */
	  return typed(name, {
	    Array: function (X) {
	      const r = _schur(matrix(X));
	      return {
	        U: r.U.valueOf(),
	        T: r.T.valueOf()
	      };
	    },
	    Matrix: function (X) {
	      return _schur(X);
	    }
	  });
	  function _schur(X) {
	    const n = X.size()[0];
	    let A = X;
	    let U = identity(n);
	    let k = 0;
	    let A0;
	    do {
	      A0 = A;
	      const QR = qr(A);
	      const Q = QR.Q;
	      const R = QR.R;
	      A = multiply(R, Q);
	      U = multiply(U, Q);
	      if (k++ > 100) {
	        break;
	      }
	    } while (norm(subtract(A, A0)) > 1e-4);
	    return {
	      U,
	      T: A
	    };
	  }
	});
	return schur;
}

var lyap = {};

var hasRequiredLyap;

function requireLyap () {
	if (hasRequiredLyap) return lyap;
	hasRequiredLyap = 1;

	Object.defineProperty(lyap, "__esModule", {
	  value: true
	});
	lyap.createLyap = void 0;
	var _factory = requireFactory$b();
	const name = 'lyap';
	const dependencies = ['typed', 'matrix', 'sylvester', 'multiply', 'transpose'];
	lyap.createLyap = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    sylvester,
	    multiply,
	    transpose
	  } = _ref;
	  /**
	   *
	   * Solves the Continuous-time Lyapunov equation AP+PA'+Q=0 for P, where
	   * Q is an input matrix. When Q is symmetric, P is also symmetric. Notice
	   * that different equivalent definitions exist for the Continuous-time
	   * Lyapunov equation.
	   * https://en.wikipedia.org/wiki/Lyapunov_equation
	   *
	   * Syntax:
	   *
	   *     math.lyap(A, Q)
	   *
	   * Examples:
	   *
	   *     const A = [[-2, 0], [1, -4]]
	   *     const Q = [[3, 1], [1, 3]]
	   *     const P = math.lyap(A, Q)
	   *
	   * See also:
	   *
	   *     sylvester, schur
	   *
	   * @param {Matrix | Array} A  Matrix A
	   * @param {Matrix | Array} Q  Matrix Q
	   * @return {Matrix | Array} Matrix P solution to the Continuous-time Lyapunov equation AP+PA'=Q
	   */
	  return typed(name, {
	    'Matrix, Matrix': function (A, Q) {
	      return sylvester(A, transpose(A), multiply(-1, Q));
	    },
	    'Array, Matrix': function (A, Q) {
	      return sylvester(matrix(A), transpose(matrix(A)), multiply(-1, Q));
	    },
	    'Matrix, Array': function (A, Q) {
	      return sylvester(A, transpose(matrix(A)), matrix(multiply(-1, Q)));
	    },
	    'Array, Array': function (A, Q) {
	      return sylvester(matrix(A), transpose(matrix(A)), matrix(multiply(-1, Q))).toArray();
	    }
	  });
	});
	return lyap;
}

var divide = {};

var hasRequiredDivide;

function requireDivide () {
	if (hasRequiredDivide) return divide;
	hasRequiredDivide = 1;

	Object.defineProperty(divide, "__esModule", {
	  value: true
	});
	divide.createDivide = void 0;
	var _factory = requireFactory$b();
	var _object = requireObject$3();
	var _matAlgo11xS0s = requireMatAlgo11xS0s();
	var _matAlgo14xDs = requireMatAlgo14xDs();
	const name = 'divide';
	const dependencies = ['typed', 'matrix', 'multiply', 'equalScalar', 'divideScalar', 'inv'];
	divide.createDivide = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    multiply,
	    equalScalar,
	    divideScalar,
	    inv
	  } = _ref;
	  const matAlgo11xS0s = (0, _matAlgo11xS0s.createMatAlgo11xS0s)({
	    typed,
	    equalScalar
	  });
	  const matAlgo14xDs = (0, _matAlgo14xDs.createMatAlgo14xDs)({
	    typed
	  });

	  /**
	   * Divide two values, `x / y`.
	   * To divide matrices, `x` is multiplied with the inverse of `y`: `x * inv(y)`.
	   *
	   * Syntax:
	   *
	   *    math.divide(x, y)
	   *
	   * Examples:
	   *
	   *    math.divide(2, 3)            // returns number 0.6666666666666666
	   *
	   *    const a = math.complex(5, 14)
	   *    const b = math.complex(4, 1)
	   *    math.divide(a, b)            // returns Complex 2 + 3i
	   *
	   *    const c = [[7, -6], [13, -4]]
	   *    const d = [[1, 2], [4, 3]]
	   *    math.divide(c, d)            // returns Array [[-9, 4], [-11, 6]]
	   *
	   *    const e = math.unit('18 km')
	   *    math.divide(e, 4.5)          // returns Unit 4 km
	   *
	   * See also:
	   *
	   *    multiply
	   *
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix} x   Numerator
	   * @param  {number | BigNumber | bigint | Fraction | Complex | Array | Matrix} y          Denominator
	   * @return {number | BigNumber | bigint | Fraction | Complex | Unit | Array | Matrix}                      Quotient, `x / y`
	   */
	  return typed('divide', (0, _object.extend)({
	    // we extend the signatures of divideScalar with signatures dealing with matrices

	    'Array | Matrix, Array | Matrix': function (x, y) {
	      // TODO: implement matrix right division using pseudo inverse
	      // https://www.mathworks.nl/help/matlab/ref/mrdivide.html
	      // https://www.gnu.org/software/octave/doc/interpreter/Arithmetic-Ops.html
	      // https://stackoverflow.com/questions/12263932/how-does-gnu-octave-matrix-division-work-getting-unexpected-behaviour
	      return multiply(x, inv(y));
	    },
	    'DenseMatrix, any': function (x, y) {
	      return matAlgo14xDs(x, y, divideScalar, false);
	    },
	    'SparseMatrix, any': function (x, y) {
	      return matAlgo11xS0s(x, y, divideScalar, false);
	    },
	    'Array, any': function (x, y) {
	      // use matrix implementation
	      return matAlgo14xDs(matrix(x), y, divideScalar, false).valueOf();
	    },
	    'any, Array | Matrix': function (x, y) {
	      return multiply(x, inv(y));
	    }
	  }, divideScalar.signatures));
	});
	return divide;
}

var distance$1 = {};

var hasRequiredDistance;

function requireDistance () {
	if (hasRequiredDistance) return distance$1;
	hasRequiredDistance = 1;

	Object.defineProperty(distance$1, "__esModule", {
	  value: true
	});
	distance$1.createDistance = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'distance';
	const dependencies = ['typed', 'addScalar', 'subtractScalar', 'divideScalar', 'multiplyScalar', 'deepEqual', 'sqrt', 'abs'];
	distance$1.createDistance = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    addScalar,
	    subtractScalar,
	    multiplyScalar,
	    divideScalar,
	    deepEqual,
	    sqrt,
	    abs
	  } = _ref;
	  /**
	    * Calculates:
	    *    The eucledian distance between two points in N-dimensional spaces.
	    *    Distance between point and a line in 2 and 3 dimensional spaces.
	    *    Pairwise distance between a set of 2D or 3D points
	    * NOTE:
	    *    When substituting coefficients of a line(a, b and c), use ax + by + c = 0 instead of ax + by = c
	    *    For parametric equation of a 3D line, x0, y0, z0, a, b, c are from: (x−x0, y−y0, z−z0) = t(a, b, c)
	    *
	    * Syntax:
	    *
	    *    math.distance([x1,y1], [x2,y2])
	    *    math.distance({pointOneX, pointOneY}, {pointTwoX, pointTwoY})
	    *    math.distance([x1,y1,z1], [x2,y2,z2])
	    *    math.distance({pointOneX, pointOneY, pointOneZ}, {pointTwoX, pointTwoY, pointTwoZ})
	    *    math.distance([x1,y1,z1,a1], [x2,y2,z2,a2])
	    *    math.distance([[x1,y1], [x2,y2], [x3,y3]])
	    *    math.distance([[x1,y1,z1], [x2,y2,z2], [x3,y3,z3]])
	    *    math.distance([pointX,pointY], [a,b,c])
	    *    math.distance([pointX,pointY], [lineOnePtX,lineOnePtY], [lineTwoPtX,lineTwoPtY])
	    *    math.distance({pointX, pointY}, {lineOnePtX, lineOnePtY}, {lineTwoPtX, lineTwoPtY})
	    *    math.distance([pointX,pointY,pointZ], [x0, y0, z0, a, b, c])
	    *    math.distance({pointX, pointY, pointZ}, {x0, y0, z0, a, b, c})
	    *
	    * Examples:
	    *    math.distance([0,0], [4,4])                     // Returns 5.656854249492381
	    *    math.distance(
	    *     {pointOneX: 0, pointOneY: 0},
	    *     {pointTwoX: 10, pointTwoY: 10})                // Returns 14.142135623730951
	    *    math.distance([1, 0, 1], [4, -2, 2])            // Returns 3.7416573867739413
	    *    math.distance(
	    *     {pointOneX: 4, pointOneY: 5, pointOneZ: 8},
	    *     {pointTwoX: 2, pointTwoY: 7, pointTwoZ: 9})    // Returns 3
	    *    math.distance([1, 0, 1, 0], [0, -1, 0, -1])     // Returns 2
	    *    math.distance([[1, 2], [1, 2], [1, 3]])         // Returns [0, 1, 1]
	    *    math.distance([[1,2,4], [1,2,6], [8,1,3]])      // Returns [2, 7.14142842854285, 7.681145747868608]
	    *    math.distance([10, 10], [8, 1, 3])              // Returns 11.535230316796387
	    *    math.distance([0, 0], [3, 0], [0, 4])        // Returns 2.4
	    *    math.distance(
	    *     {pointX: 0, pointY: 0},
	    *     {lineOnePtX: 3, lineOnePtY: 0},
	    *     {lineTwoPtX: 0, lineTwoPtY: 4})                // Returns 2.4
	    *    math.distance([2, 3, 1], [1, 1, 2, 5, 0, 1])    // Returns 2.3204774044612857
	    *    math.distance(
	    *     {pointX: 2, pointY: 3, pointZ: 1},
	    *     {x0: 1, y0: 1, z0: 2, a: 5, b: 0, c: 1})       // Returns 2.3204774044612857
	    *
	    * @param {Array | Matrix | Object} x    Co-ordinates of first point
	    * @param {Array | Matrix | Object} y    Co-ordinates of second point
	    * @return {Number | BigNumber} Returns the distance from two/three points
	  */
	  return typed(name, {
	    'Array, Array, Array': function (x, y, z) {
	      // Point to Line 2D (x=Point, y=LinePoint1, z=LinePoint2)
	      if (x.length === 2 && y.length === 2 && z.length === 2) {
	        if (!_2d(x)) {
	          throw new TypeError('Array with 2 numbers or BigNumbers expected for first argument');
	        }
	        if (!_2d(y)) {
	          throw new TypeError('Array with 2 numbers or BigNumbers expected for second argument');
	        }
	        if (!_2d(z)) {
	          throw new TypeError('Array with 2 numbers or BigNumbers expected for third argument');
	        }
	        if (deepEqual(y, z)) {
	          throw new TypeError('LinePoint1 should not be same with LinePoint2');
	        }
	        const xCoeff = subtractScalar(z[1], y[1]);
	        const yCoeff = subtractScalar(y[0], z[0]);
	        const constant = subtractScalar(multiplyScalar(z[0], y[1]), multiplyScalar(y[0], z[1]));
	        return _distancePointLine2D(x[0], x[1], xCoeff, yCoeff, constant);
	      } else {
	        throw new TypeError('Invalid Arguments: Try again');
	      }
	    },
	    'Object, Object, Object': function (x, y, z) {
	      if (Object.keys(x).length === 2 && Object.keys(y).length === 2 && Object.keys(z).length === 2) {
	        if (!_2d(x)) {
	          throw new TypeError('Values of pointX and pointY should be numbers or BigNumbers');
	        }
	        if (!_2d(y)) {
	          throw new TypeError('Values of lineOnePtX and lineOnePtY should be numbers or BigNumbers');
	        }
	        if (!_2d(z)) {
	          throw new TypeError('Values of lineTwoPtX and lineTwoPtY should be numbers or BigNumbers');
	        }
	        if (deepEqual(_objectToArray(y), _objectToArray(z))) {
	          throw new TypeError('LinePoint1 should not be same with LinePoint2');
	        }
	        if ('pointX' in x && 'pointY' in x && 'lineOnePtX' in y && 'lineOnePtY' in y && 'lineTwoPtX' in z && 'lineTwoPtY' in z) {
	          const xCoeff = subtractScalar(z.lineTwoPtY, y.lineOnePtY);
	          const yCoeff = subtractScalar(y.lineOnePtX, z.lineTwoPtX);
	          const constant = subtractScalar(multiplyScalar(z.lineTwoPtX, y.lineOnePtY), multiplyScalar(y.lineOnePtX, z.lineTwoPtY));
	          return _distancePointLine2D(x.pointX, x.pointY, xCoeff, yCoeff, constant);
	        } else {
	          throw new TypeError('Key names do not match');
	        }
	      } else {
	        throw new TypeError('Invalid Arguments: Try again');
	      }
	    },
	    'Array, Array': function (x, y) {
	      // Point to Line 2D (x=[pointX, pointY], y=[x-coeff, y-coeff, const])
	      if (x.length === 2 && y.length === 3) {
	        if (!_2d(x)) {
	          throw new TypeError('Array with 2 numbers or BigNumbers expected for first argument');
	        }
	        if (!_3d(y)) {
	          throw new TypeError('Array with 3 numbers or BigNumbers expected for second argument');
	        }
	        return _distancePointLine2D(x[0], x[1], y[0], y[1], y[2]);
	      } else if (x.length === 3 && y.length === 6) {
	        // Point to Line 3D
	        if (!_3d(x)) {
	          throw new TypeError('Array with 3 numbers or BigNumbers expected for first argument');
	        }
	        if (!_parametricLine(y)) {
	          throw new TypeError('Array with 6 numbers or BigNumbers expected for second argument');
	        }
	        return _distancePointLine3D(x[0], x[1], x[2], y[0], y[1], y[2], y[3], y[4], y[5]);
	      } else if (x.length === y.length && x.length > 0) {
	        // Point to Point N-dimensions
	        if (!_containsOnlyNumbers(x)) {
	          throw new TypeError('All values of an array should be numbers or BigNumbers');
	        }
	        if (!_containsOnlyNumbers(y)) {
	          throw new TypeError('All values of an array should be numbers or BigNumbers');
	        }
	        return _euclideanDistance(x, y);
	      } else {
	        throw new TypeError('Invalid Arguments: Try again');
	      }
	    },
	    'Object, Object': function (x, y) {
	      if (Object.keys(x).length === 2 && Object.keys(y).length === 3) {
	        if (!_2d(x)) {
	          throw new TypeError('Values of pointX and pointY should be numbers or BigNumbers');
	        }
	        if (!_3d(y)) {
	          throw new TypeError('Values of xCoeffLine, yCoeffLine and constant should be numbers or BigNumbers');
	        }
	        if ('pointX' in x && 'pointY' in x && 'xCoeffLine' in y && 'yCoeffLine' in y && 'constant' in y) {
	          return _distancePointLine2D(x.pointX, x.pointY, y.xCoeffLine, y.yCoeffLine, y.constant);
	        } else {
	          throw new TypeError('Key names do not match');
	        }
	      } else if (Object.keys(x).length === 3 && Object.keys(y).length === 6) {
	        // Point to Line 3D
	        if (!_3d(x)) {
	          throw new TypeError('Values of pointX, pointY and pointZ should be numbers or BigNumbers');
	        }
	        if (!_parametricLine(y)) {
	          throw new TypeError('Values of x0, y0, z0, a, b and c should be numbers or BigNumbers');
	        }
	        if ('pointX' in x && 'pointY' in x && 'x0' in y && 'y0' in y && 'z0' in y && 'a' in y && 'b' in y && 'c' in y) {
	          return _distancePointLine3D(x.pointX, x.pointY, x.pointZ, y.x0, y.y0, y.z0, y.a, y.b, y.c);
	        } else {
	          throw new TypeError('Key names do not match');
	        }
	      } else if (Object.keys(x).length === 2 && Object.keys(y).length === 2) {
	        // Point to Point 2D
	        if (!_2d(x)) {
	          throw new TypeError('Values of pointOneX and pointOneY should be numbers or BigNumbers');
	        }
	        if (!_2d(y)) {
	          throw new TypeError('Values of pointTwoX and pointTwoY should be numbers or BigNumbers');
	        }
	        if ('pointOneX' in x && 'pointOneY' in x && 'pointTwoX' in y && 'pointTwoY' in y) {
	          return _euclideanDistance([x.pointOneX, x.pointOneY], [y.pointTwoX, y.pointTwoY]);
	        } else {
	          throw new TypeError('Key names do not match');
	        }
	      } else if (Object.keys(x).length === 3 && Object.keys(y).length === 3) {
	        // Point to Point 3D
	        if (!_3d(x)) {
	          throw new TypeError('Values of pointOneX, pointOneY and pointOneZ should be numbers or BigNumbers');
	        }
	        if (!_3d(y)) {
	          throw new TypeError('Values of pointTwoX, pointTwoY and pointTwoZ should be numbers or BigNumbers');
	        }
	        if ('pointOneX' in x && 'pointOneY' in x && 'pointOneZ' in x && 'pointTwoX' in y && 'pointTwoY' in y && 'pointTwoZ' in y) {
	          return _euclideanDistance([x.pointOneX, x.pointOneY, x.pointOneZ], [y.pointTwoX, y.pointTwoY, y.pointTwoZ]);
	        } else {
	          throw new TypeError('Key names do not match');
	        }
	      } else {
	        throw new TypeError('Invalid Arguments: Try again');
	      }
	    },
	    Array: function (arr) {
	      if (!_pairwise(arr)) {
	        throw new TypeError('Incorrect array format entered for pairwise distance calculation');
	      }
	      return _distancePairwise(arr);
	    }
	  });
	  function _isNumber(a) {
	    // distance supports numbers and bignumbers
	    return typeof a === 'number' || (0, _is.isBigNumber)(a);
	  }
	  function _2d(a) {
	    // checks if the number of arguments are correct in count and are valid (should be numbers)
	    if (a.constructor !== Array) {
	      a = _objectToArray(a);
	    }
	    return _isNumber(a[0]) && _isNumber(a[1]);
	  }
	  function _3d(a) {
	    // checks if the number of arguments are correct in count and are valid (should be numbers)
	    if (a.constructor !== Array) {
	      a = _objectToArray(a);
	    }
	    return _isNumber(a[0]) && _isNumber(a[1]) && _isNumber(a[2]);
	  }
	  function _containsOnlyNumbers(a) {
	    // checks if the number of arguments are correct in count and are valid (should be numbers)
	    if (!Array.isArray(a)) {
	      a = _objectToArray(a);
	    }
	    return a.every(_isNumber);
	  }
	  function _parametricLine(a) {
	    if (a.constructor !== Array) {
	      a = _objectToArray(a);
	    }
	    return _isNumber(a[0]) && _isNumber(a[1]) && _isNumber(a[2]) && _isNumber(a[3]) && _isNumber(a[4]) && _isNumber(a[5]);
	  }
	  function _objectToArray(o) {
	    const keys = Object.keys(o);
	    const a = [];
	    for (let i = 0; i < keys.length; i++) {
	      a.push(o[keys[i]]);
	    }
	    return a;
	  }
	  function _pairwise(a) {
	    // checks for valid arguments passed to _distancePairwise(Array)
	    if (a[0].length === 2 && _isNumber(a[0][0]) && _isNumber(a[0][1])) {
	      if (a.some(aI => aI.length !== 2 || !_isNumber(aI[0]) || !_isNumber(aI[1]))) {
	        return false;
	      }
	    } else if (a[0].length === 3 && _isNumber(a[0][0]) && _isNumber(a[0][1]) && _isNumber(a[0][2])) {
	      if (a.some(aI => aI.length !== 3 || !_isNumber(aI[0]) || !_isNumber(aI[1]) || !_isNumber(aI[2]))) {
	        return false;
	      }
	    } else {
	      return false;
	    }
	    return true;
	  }
	  function _distancePointLine2D(x, y, a, b, c) {
	    const num = abs(addScalar(addScalar(multiplyScalar(a, x), multiplyScalar(b, y)), c));
	    const den = sqrt(addScalar(multiplyScalar(a, a), multiplyScalar(b, b)));
	    return divideScalar(num, den);
	  }
	  function _distancePointLine3D(x, y, z, x0, y0, z0, a, b, c) {
	    let num = [subtractScalar(multiplyScalar(subtractScalar(y0, y), c), multiplyScalar(subtractScalar(z0, z), b)), subtractScalar(multiplyScalar(subtractScalar(z0, z), a), multiplyScalar(subtractScalar(x0, x), c)), subtractScalar(multiplyScalar(subtractScalar(x0, x), b), multiplyScalar(subtractScalar(y0, y), a))];
	    num = sqrt(addScalar(addScalar(multiplyScalar(num[0], num[0]), multiplyScalar(num[1], num[1])), multiplyScalar(num[2], num[2])));
	    const den = sqrt(addScalar(addScalar(multiplyScalar(a, a), multiplyScalar(b, b)), multiplyScalar(c, c)));
	    return divideScalar(num, den);
	  }
	  function _euclideanDistance(x, y) {
	    const vectorSize = x.length;
	    let result = 0;
	    let diff = 0;
	    for (let i = 0; i < vectorSize; i++) {
	      diff = subtractScalar(x[i], y[i]);
	      result = addScalar(multiplyScalar(diff, diff), result);
	    }
	    return sqrt(result);
	  }
	  function _distancePairwise(a) {
	    const result = [];
	    let pointA = [];
	    let pointB = [];
	    for (let i = 0; i < a.length - 1; i++) {
	      for (let j = i + 1; j < a.length; j++) {
	        if (a[0].length === 2) {
	          pointA = [a[i][0], a[i][1]];
	          pointB = [a[j][0], a[j][1]];
	        } else if (a[0].length === 3) {
	          pointA = [a[i][0], a[i][1], a[i][2]];
	          pointB = [a[j][0], a[j][1], a[j][2]];
	        }
	        result.push(_euclideanDistance(pointA, pointB));
	      }
	    }
	    return result;
	  }
	});
	return distance$1;
}

var intersect$2 = {};

var hasRequiredIntersect;

function requireIntersect () {
	if (hasRequiredIntersect) return intersect$2;
	hasRequiredIntersect = 1;

	Object.defineProperty(intersect$2, "__esModule", {
	  value: true
	});
	intersect$2.createIntersect = void 0;
	var _factory = requireFactory$b();
	const name = 'intersect';
	const dependencies = ['typed', 'config', 'abs', 'add', 'addScalar', 'matrix', 'multiply', 'multiplyScalar', 'divideScalar', 'subtract', 'smaller', 'equalScalar', 'flatten', 'isZero', 'isNumeric'];
	intersect$2.createIntersect = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    abs,
	    add,
	    addScalar,
	    matrix,
	    multiply,
	    multiplyScalar,
	    divideScalar,
	    subtract,
	    smaller,
	    equalScalar,
	    flatten,
	    isZero,
	    isNumeric
	  } = _ref;
	  /**
	   * Calculates the point of intersection of two lines in two or three dimensions
	   * and of a line and a plane in three dimensions. The inputs are in the form of
	   * arrays or 1 dimensional matrices. The line intersection functions return null
	   * if the lines do not meet.
	   *
	   * Note: Fill the plane coefficients as `x + y + z = c` and not as `x + y + z + c = 0`.
	   *
	   * Syntax:
	   *
	   *    math.intersect(endPoint1Line1, endPoint2Line1, endPoint1Line2, endPoint2Line2)
	   *    math.intersect(endPoint1, endPoint2, planeCoefficients)
	   *
	   * Examples:
	   *
	   *    math.intersect([0, 0], [10, 10], [10, 0], [0, 10])              // Returns [5, 5]
	   *    math.intersect([0, 0, 0], [10, 10, 0], [10, 0, 0], [0, 10, 0])  // Returns [5, 5, 0]
	   *    math.intersect([1, 0, 1],  [4, -2, 2], [1, 1, 1, 6])            // Returns [7, -4, 3]
	   *
	   * @param  {Array | Matrix} w   Co-ordinates of first end-point of first line
	   * @param  {Array | Matrix} x   Co-ordinates of second end-point of first line
	   * @param  {Array | Matrix} y   Co-ordinates of first end-point of second line
	   *                              OR Co-efficients of the plane's equation
	   * @param  {Array | Matrix} z   Co-ordinates of second end-point of second line
	   *                              OR undefined if the calculation is for line and plane
	   * @return {Array}              Returns the point of intersection of lines/lines-planes
	   */
	  return typed('intersect', {
	    'Array, Array, Array': _AAA,
	    'Array, Array, Array, Array': _AAAA,
	    'Matrix, Matrix, Matrix': function (x, y, plane) {
	      const arr = _AAA(x.valueOf(), y.valueOf(), plane.valueOf());
	      return arr === null ? null : matrix(arr);
	    },
	    'Matrix, Matrix, Matrix, Matrix': function (w, x, y, z) {
	      // TODO: output matrix type should match input matrix type
	      const arr = _AAAA(w.valueOf(), x.valueOf(), y.valueOf(), z.valueOf());
	      return arr === null ? null : matrix(arr);
	    }
	  });
	  function _AAA(x, y, plane) {
	    x = _coerceArr(x);
	    y = _coerceArr(y);
	    plane = _coerceArr(plane);
	    if (!_3d(x)) {
	      throw new TypeError('Array with 3 numbers or BigNumbers expected for first argument');
	    }
	    if (!_3d(y)) {
	      throw new TypeError('Array with 3 numbers or BigNumbers expected for second argument');
	    }
	    if (!_4d(plane)) {
	      throw new TypeError('Array with 4 numbers expected as third argument');
	    }
	    return _intersectLinePlane(x[0], x[1], x[2], y[0], y[1], y[2], plane[0], plane[1], plane[2], plane[3]);
	  }
	  function _AAAA(w, x, y, z) {
	    w = _coerceArr(w);
	    x = _coerceArr(x);
	    y = _coerceArr(y);
	    z = _coerceArr(z);
	    if (w.length === 2) {
	      if (!_2d(w)) {
	        throw new TypeError('Array with 2 numbers or BigNumbers expected for first argument');
	      }
	      if (!_2d(x)) {
	        throw new TypeError('Array with 2 numbers or BigNumbers expected for second argument');
	      }
	      if (!_2d(y)) {
	        throw new TypeError('Array with 2 numbers or BigNumbers expected for third argument');
	      }
	      if (!_2d(z)) {
	        throw new TypeError('Array with 2 numbers or BigNumbers expected for fourth argument');
	      }
	      return _intersect2d(w, x, y, z);
	    } else if (w.length === 3) {
	      if (!_3d(w)) {
	        throw new TypeError('Array with 3 numbers or BigNumbers expected for first argument');
	      }
	      if (!_3d(x)) {
	        throw new TypeError('Array with 3 numbers or BigNumbers expected for second argument');
	      }
	      if (!_3d(y)) {
	        throw new TypeError('Array with 3 numbers or BigNumbers expected for third argument');
	      }
	      if (!_3d(z)) {
	        throw new TypeError('Array with 3 numbers or BigNumbers expected for fourth argument');
	      }
	      return _intersect3d(w[0], w[1], w[2], x[0], x[1], x[2], y[0], y[1], y[2], z[0], z[1], z[2]);
	    } else {
	      throw new TypeError('Arrays with two or thee dimensional points expected');
	    }
	  }

	  /** Coerce row and column 2-dim arrays to 1-dim array */
	  function _coerceArr(arr) {
	    // row matrix
	    if (arr.length === 1) return arr[0];

	    // column matrix
	    if (arr.length > 1 && Array.isArray(arr[0])) {
	      if (arr.every(el => Array.isArray(el) && el.length === 1)) return flatten(arr);
	    }
	    return arr;
	  }
	  function _2d(x) {
	    return x.length === 2 && isNumeric(x[0]) && isNumeric(x[1]);
	  }
	  function _3d(x) {
	    return x.length === 3 && isNumeric(x[0]) && isNumeric(x[1]) && isNumeric(x[2]);
	  }
	  function _4d(x) {
	    return x.length === 4 && isNumeric(x[0]) && isNumeric(x[1]) && isNumeric(x[2]) && isNumeric(x[3]);
	  }
	  function _intersect2d(p1a, p1b, p2a, p2b) {
	    const o1 = p1a;
	    const o2 = p2a;
	    const d1 = subtract(o1, p1b);
	    const d2 = subtract(o2, p2b);
	    const det = subtract(multiplyScalar(d1[0], d2[1]), multiplyScalar(d2[0], d1[1]));
	    if (isZero(det)) return null;
	    if (smaller(abs(det), config.relTol)) {
	      return null;
	    }
	    const d20o11 = multiplyScalar(d2[0], o1[1]);
	    const d21o10 = multiplyScalar(d2[1], o1[0]);
	    const d20o21 = multiplyScalar(d2[0], o2[1]);
	    const d21o20 = multiplyScalar(d2[1], o2[0]);
	    const t = divideScalar(addScalar(subtract(subtract(d20o11, d21o10), d20o21), d21o20), det);
	    return add(multiply(d1, t), o1);
	  }
	  function _intersect3dHelper(a, b, c, d, e, f, g, h, i, j, k, l) {
	    // (a - b)*(c - d) + (e - f)*(g - h) + (i - j)*(k - l)
	    const add1 = multiplyScalar(subtract(a, b), subtract(c, d));
	    const add2 = multiplyScalar(subtract(e, f), subtract(g, h));
	    const add3 = multiplyScalar(subtract(i, j), subtract(k, l));
	    return addScalar(addScalar(add1, add2), add3);
	  }
	  function _intersect3d(x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4) {
	    const d1343 = _intersect3dHelper(x1, x3, x4, x3, y1, y3, y4, y3, z1, z3, z4, z3);
	    const d4321 = _intersect3dHelper(x4, x3, x2, x1, y4, y3, y2, y1, z4, z3, z2, z1);
	    const d1321 = _intersect3dHelper(x1, x3, x2, x1, y1, y3, y2, y1, z1, z3, z2, z1);
	    const d4343 = _intersect3dHelper(x4, x3, x4, x3, y4, y3, y4, y3, z4, z3, z4, z3);
	    const d2121 = _intersect3dHelper(x2, x1, x2, x1, y2, y1, y2, y1, z2, z1, z2, z1);
	    const numerator = subtract(multiplyScalar(d1343, d4321), multiplyScalar(d1321, d4343));
	    const denominator = subtract(multiplyScalar(d2121, d4343), multiplyScalar(d4321, d4321));
	    if (isZero(denominator)) return null;
	    const ta = divideScalar(numerator, denominator);
	    const tb = divideScalar(addScalar(d1343, multiplyScalar(ta, d4321)), d4343);
	    const pax = addScalar(x1, multiplyScalar(ta, subtract(x2, x1)));
	    const pay = addScalar(y1, multiplyScalar(ta, subtract(y2, y1)));
	    const paz = addScalar(z1, multiplyScalar(ta, subtract(z2, z1)));
	    const pbx = addScalar(x3, multiplyScalar(tb, subtract(x4, x3)));
	    const pby = addScalar(y3, multiplyScalar(tb, subtract(y4, y3)));
	    const pbz = addScalar(z3, multiplyScalar(tb, subtract(z4, z3)));
	    if (equalScalar(pax, pbx) && equalScalar(pay, pby) && equalScalar(paz, pbz)) {
	      return [pax, pay, paz];
	    } else {
	      return null;
	    }
	  }
	  function _intersectLinePlane(x1, y1, z1, x2, y2, z2, x, y, z, c) {
	    const x1x = multiplyScalar(x1, x);
	    const x2x = multiplyScalar(x2, x);
	    const y1y = multiplyScalar(y1, y);
	    const y2y = multiplyScalar(y2, y);
	    const z1z = multiplyScalar(z1, z);
	    const z2z = multiplyScalar(z2, z);
	    const numerator = subtract(subtract(subtract(c, x1x), y1y), z1z);
	    const denominator = subtract(subtract(subtract(addScalar(addScalar(x2x, y2y), z2z), x1x), y1y), z1z);
	    const t = divideScalar(numerator, denominator);
	    const px = addScalar(x1, multiplyScalar(t, subtract(x2, x1)));
	    const py = addScalar(y1, multiplyScalar(t, subtract(y2, y1)));
	    const pz = addScalar(z1, multiplyScalar(t, subtract(z2, z1)));
	    return [px, py, pz];
	    // TODO: Add cases when line is parallel to the plane:
	    //       (a) no intersection,
	    //       (b) line contained in plane
	  }
	});
	return intersect$2;
}

var sum = {};

var hasRequiredSum;

function requireSum () {
	if (hasRequiredSum) return sum;
	hasRequiredSum = 1;

	Object.defineProperty(sum, "__esModule", {
	  value: true
	});
	sum.createSum = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'sum';
	const dependencies = ['typed', 'config', 'add', 'numeric'];
	sum.createSum = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    add,
	    numeric
	  } = _ref;
	  /**
	   * Compute the sum of a matrix or a list with values.
	   * In case of a multidimensional array or matrix, the sum of all
	   * elements will be calculated.
	   *
	   * Syntax:
	   *
	   *     math.sum(a, b, c, ...)
	   *     math.sum(A)
	   *     math.sum(A, dimension)
	   *
	   * Examples:
	   *
	   *     math.sum(2, 1, 4, 3)               // returns 10
	   *     math.sum([2, 1, 4, 3])             // returns 10
	   *     math.sum([[2, 5], [4, 3], [1, 7]]) // returns 22
	   *
	   * See also:
	   *
	   *    mean, median, min, max, prod, std, variance, cumsum
	   *
	   * @param {... *} args  A single matrix or multiple scalar values
	   * @return {*} The sum of all values
	   */
	  return typed(name, {
	    // sum([a, b, c, d, ...])
	    'Array | Matrix': _sum,
	    // sum([a, b, c, d, ...], dim)
	    'Array | Matrix, number | BigNumber': _nsumDim,
	    // sum(a, b, c, d, ...)
	    '...': function (args) {
	      if ((0, _collection.containsCollections)(args)) {
	        throw new TypeError('Scalar values expected in function sum');
	      }
	      return _sum(args);
	    }
	  });

	  /**
	   * Recursively calculate the sum of an n-dimensional array
	   * @param {Array | Matrix} array
	   * @return {number} sum
	   * @private
	   */
	  function _sum(array) {
	    let sum;
	    (0, _collection.deepForEach)(array, function (value) {
	      try {
	        sum = sum === undefined ? value : add(sum, value);
	      } catch (err) {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'sum', value);
	      }
	    });

	    // make sure returning numeric value: parse a string into a numeric value
	    if (sum === undefined) {
	      sum = numeric(0, config.number);
	    }
	    if (typeof sum === 'string') {
	      sum = numeric(sum, (0, _number.safeNumberType)(sum, config));
	    }
	    return sum;
	  }
	  function _nsumDim(array, dim) {
	    try {
	      const sum = (0, _collection.reduce)(array, dim, add);
	      return sum;
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, 'sum');
	    }
	  }
	});
	return sum;
}

var cumsum = {};

var hasRequiredCumsum;

function requireCumsum () {
	if (hasRequiredCumsum) return cumsum;
	hasRequiredCumsum = 1;

	Object.defineProperty(cumsum, "__esModule", {
	  value: true
	});
	cumsum.createCumSum = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	var _switch2 = require_switch();
	var _improveErrorMessage = requireImproveErrorMessage();
	var _array = requireArray();
	var _IndexError = requireIndexError();
	const name = 'cumsum';
	const dependencies = ['typed', 'add', 'unaryPlus'];
	cumsum.createCumSum = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    unaryPlus
	  } = _ref;
	  /**
	   * Compute the cumulative sum of a matrix or a list with values.
	   * In case of a (multi dimensional) array or matrix, the cumulative sums
	   * along a specified dimension (defaulting to the first) will be calculated.
	   *
	   * Syntax:
	   *
	   *     math.cumsum(a, b, c, ...)
	   *     math.cumsum(A)
	   *
	   * Examples:
	   *
	   *     math.cumsum(2, 1, 4, 3)               // returns [2, 3, 7, 10]
	   *     math.cumsum([2, 1, 4, 3])             // returns [2, 3, 7, 10]
	   *     math.cumsum([[1, 2], [3, 4]])         // returns [[1, 2], [4, 6]]
	   *     math.cumsum([[1, 2], [3, 4]], 0)      // returns [[1, 2], [4, 6]]
	   *     math.cumsum([[1, 2], [3, 4]], 1)      // returns [[1, 3], [3, 7]]
	   *     math.cumsum([[2, 5], [4, 3], [1, 7]]) // returns [[2, 5], [6, 8], [7, 15]]
	   *
	   * See also:
	   *
	   *    mean, median, min, max, prod, std, variance, sum
	   *
	   * @param {... *} args  A single matrix or or multiple scalar values
	   * @return {*} The cumulative sum of all values
	   */
	  return typed(name, {
	    // sum([a, b, c, d, ...])
	    Array: _cumsum,
	    Matrix: function (matrix) {
	      return matrix.create(_cumsum(matrix.valueOf(), matrix.datatype()));
	    },
	    // sum([a, b, c, d, ...], dim)
	    'Array, number | BigNumber': _ncumSumDim,
	    'Matrix, number | BigNumber': function (matrix, dim) {
	      return matrix.create(_ncumSumDim(matrix.valueOf(), dim), matrix.datatype());
	    },
	    // cumsum(a, b, c, d, ...)
	    '...': function (args) {
	      if ((0, _collection.containsCollections)(args)) {
	        throw new TypeError('All values expected to be scalar in function cumsum');
	      }
	      return _cumsum(args);
	    }
	  });

	  /**
	     * Recursively calculate the cumulative sum of an n-dimensional array
	     * @param {Array} array
	     * @return {number} cumsum
	     * @private
	     */
	  function _cumsum(array) {
	    try {
	      return _cumsummap(array);
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, name);
	    }
	  }
	  function _cumsummap(array) {
	    if (array.length === 0) {
	      return [];
	    }
	    const sums = [unaryPlus(array[0])]; // unaryPlus converts to number if need be
	    for (let i = 1; i < array.length; ++i) {
	      // Must use add below and not addScalar for the case of summing a
	      // 2+-dimensional array along the 0th dimension (the row vectors,
	      // or higher-d analogues, are literally added to each other).
	      sums.push(add(sums[i - 1], array[i]));
	    }
	    return sums;
	  }
	  function _ncumSumDim(array, dim) {
	    const size = (0, _array.arraySize)(array);
	    if (dim < 0 || dim >= size.length) {
	      // TODO: would be more clear when throwing a DimensionError here
	      throw new _IndexError.IndexError(dim, size.length);
	    }
	    try {
	      return _cumsumDimensional(array, dim);
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, name);
	    }
	  }

	  /* Possible TODO: Refactor _reduce in collection.js to be able to work here as well */
	  function _cumsumDimensional(mat, dim) {
	    let i, ret, tran;
	    if (dim <= 0) {
	      const initialValue = mat[0][0];
	      if (!Array.isArray(initialValue)) {
	        return _cumsummap(mat);
	      } else {
	        tran = (0, _switch2._switch)(mat);
	        ret = [];
	        for (i = 0; i < tran.length; i++) {
	          ret[i] = _cumsumDimensional(tran[i], dim - 1);
	        }
	        return ret;
	      }
	    } else {
	      ret = [];
	      for (i = 0; i < mat.length; i++) {
	        ret[i] = _cumsumDimensional(mat[i], dim - 1);
	      }
	      return ret;
	    }
	  }
	});
	return cumsum;
}

var mean = {};

var hasRequiredMean;

function requireMean () {
	if (hasRequiredMean) return mean;
	hasRequiredMean = 1;

	Object.defineProperty(mean, "__esModule", {
	  value: true
	});
	mean.createMean = void 0;
	var _collection = requireCollection();
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'mean';
	const dependencies = ['typed', 'add', 'divide'];
	mean.createMean = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    divide
	  } = _ref;
	  /**
	   * Compute the mean value of matrix or a list with values.
	   * In case of a multidimensional array, the mean of the flattened array
	   * will be calculated. When `dim` is provided, the maximum over the selected
	   * dimension will be calculated. Parameter `dim` is zero-based.
	   *
	   * Syntax:
	   *
	   *     math.mean(a, b, c, ...)
	   *     math.mean(A)
	   *     math.mean(A, dimension)
	   *
	   * Examples:
	   *
	   *     math.mean(2, 1, 4, 3)                     // returns 2.5
	   *     math.mean([1, 2.7, 3.2, 4])               // returns 2.725
	   *
	   *     math.mean([[2, 5], [6, 3], [1, 7]], 0)    // returns [3, 5]
	   *     math.mean([[2, 5], [6, 3], [1, 7]], 1)    // returns [3.5, 4.5, 4]
	   *
	   * See also:
	   *
	   *     median, min, max, sum, prod, std, variance
	   *
	   * @param {... *} args  A single matrix or or multiple scalar values
	   * @return {*} The mean of all values
	   */
	  return typed(name, {
	    // mean([a, b, c, d, ...])
	    'Array | Matrix': _mean,
	    // mean([a, b, c, d, ...], dim)
	    'Array | Matrix, number | BigNumber': _nmeanDim,
	    // mean(a, b, c, d, ...)
	    '...': function (args) {
	      if ((0, _collection.containsCollections)(args)) {
	        throw new TypeError('Scalar values expected in function mean');
	      }
	      return _mean(args);
	    }
	  });

	  /**
	   * Calculate the mean value in an n-dimensional array, returning a
	   * n-1 dimensional array
	   * @param {Array} array
	   * @param {number} dim
	   * @return {number} mean
	   * @private
	   */
	  function _nmeanDim(array, dim) {
	    try {
	      const sum = (0, _collection.reduce)(array, dim, add);
	      const s = Array.isArray(array) ? (0, _array.arraySize)(array) : array.size();
	      return divide(sum, s[dim]);
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, 'mean');
	    }
	  }

	  /**
	   * Recursively calculate the mean value in an n-dimensional array
	   * @param {Array} array
	   * @return {number} mean
	   * @private
	   */
	  function _mean(array) {
	    let sum;
	    let num = 0;
	    (0, _collection.deepForEach)(array, function (value) {
	      try {
	        sum = sum === undefined ? value : add(sum, value);
	        num++;
	      } catch (err) {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'mean', value);
	      }
	    });
	    if (num === 0) {
	      throw new Error('Cannot calculate the mean of an empty array');
	    }
	    return divide(sum, num);
	  }
	});
	return mean;
}

var median = {};

var hasRequiredMedian;

function requireMedian () {
	if (hasRequiredMedian) return median;
	hasRequiredMedian = 1;

	Object.defineProperty(median, "__esModule", {
	  value: true
	});
	median.createMedian = void 0;
	var _collection = requireCollection();
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'median';
	const dependencies = ['typed', 'add', 'divide', 'compare', 'partitionSelect'];
	median.createMedian = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    divide,
	    compare,
	    partitionSelect
	  } = _ref;
	  /**
	   * Recursively calculate the median of an n-dimensional array
	   * @param {Array} array
	   * @return {Number} median
	   * @private
	   */
	  function _median(array) {
	    try {
	      array = (0, _array.flatten)(array.valueOf());
	      const num = array.length;
	      if (num === 0) {
	        throw new Error('Cannot calculate median of an empty array');
	      }
	      if (num % 2 === 0) {
	        // even: return the average of the two middle values
	        const mid = num / 2 - 1;
	        const right = partitionSelect(array, mid + 1);

	        // array now partitioned at mid + 1, take max of left part
	        let left = array[mid];
	        for (let i = 0; i < mid; ++i) {
	          if (compare(array[i], left) > 0) {
	            left = array[i];
	          }
	        }
	        return middle2(left, right);
	      } else {
	        // odd: return the middle value
	        const m = partitionSelect(array, (num - 1) / 2);
	        return middle(m);
	      }
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, 'median');
	    }
	  }

	  // helper function to type check the middle value of the array
	  const middle = typed({
	    'number | BigNumber | Complex | Unit': function (value) {
	      return value;
	    }
	  });

	  // helper function to type check the two middle value of the array
	  const middle2 = typed({
	    'number | BigNumber | Complex | Unit, number | BigNumber | Complex | Unit': function (left, right) {
	      return divide(add(left, right), 2);
	    }
	  });

	  /**
	   * Compute the median of a matrix or a list with values. The values are
	   * sorted and the middle value is returned. In case of an even number of
	   * values, the average of the two middle values is returned.
	   * Supported types of values are: Number, BigNumber, Unit
	   *
	   * In case of a (multi dimensional) array or matrix, the median of all
	   * elements will be calculated.
	   *
	   * Syntax:
	   *
	   *     math.median(a, b, c, ...)
	   *     math.median(A)
	   *
	   * Examples:
	   *
	   *     math.median(5, 2, 7)        // returns 5
	   *     math.median([3, -1, 5, 7])  // returns 4
	   *
	   * See also:
	   *
	   *     mean, min, max, sum, prod, std, variance, quantileSeq
	   *
	   * @param {... *} args  A single matrix or or multiple scalar values
	   * @return {*} The median
	   */
	  return typed(name, {
	    // median([a, b, c, d, ...])
	    'Array | Matrix': _median,
	    // median([a, b, c, d, ...], dim)
	    'Array | Matrix, number | BigNumber': function (array, dim) {
	      // TODO: implement median(A, dim)
	      throw new Error('median(A, dim) is not yet supported');
	      // return reduce(arguments[0], arguments[1], ...)
	    },
	    // median(a, b, c, d, ...)
	    '...': function (args) {
	      if ((0, _collection.containsCollections)(args)) {
	        throw new TypeError('Scalar values expected in function median');
	      }
	      return _median(args);
	    }
	  });
	});
	return median;
}

var mad = {};

var hasRequiredMad;

function requireMad () {
	if (hasRequiredMad) return mad;
	hasRequiredMad = 1;

	Object.defineProperty(mad, "__esModule", {
	  value: true
	});
	mad.createMad = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _improveErrorMessage = requireImproveErrorMessage();
	const name = 'mad';
	const dependencies = ['typed', 'abs', 'map', 'median', 'subtract'];
	mad.createMad = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    abs,
	    map,
	    median,
	    subtract
	  } = _ref;
	  /**
	   * Compute the median absolute deviation of a matrix or a list with values.
	   * The median absolute deviation is defined as the median of the absolute
	   * deviations from the median.
	   *
	   * Syntax:
	   *
	   *     math.mad(a, b, c, ...)
	   *     math.mad(A)
	   *
	   * Examples:
	   *
	   *     math.mad(10, 20, 30)             // returns 10
	   *     math.mad([1, 2, 3])              // returns 1
	   *     math.mad([[1, 2, 3], [4, 5, 6]]) // returns 1.5
	   *
	   * See also:
	   *
	   *     median, mean, std, abs
	   *
	   * @param {Array | Matrix} array
	   *                        A single matrix or multiple scalar values.
	   * @return {*} The median absolute deviation.
	   */
	  return typed(name, {
	    // mad([a, b, c, d, ...])
	    'Array | Matrix': _mad,
	    // mad(a, b, c, d, ...)
	    '...': function (args) {
	      return _mad(args);
	    }
	  });
	  function _mad(array) {
	    array = (0, _array.flatten)(array.valueOf());
	    if (array.length === 0) {
	      throw new Error('Cannot calculate median absolute deviation (mad) of an empty array');
	    }
	    try {
	      const med = median(array);
	      return median(map(array, function (value) {
	        return abs(subtract(value, med));
	      }));
	    } catch (err) {
	      if (err instanceof TypeError && err.message.includes('median')) {
	        throw new TypeError(err.message.replace('median', 'mad'));
	      } else {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'mad');
	      }
	    }
	  }
	});
	return mad;
}

var variance = {};

var hasRequiredVariance;

function requireVariance () {
	if (hasRequiredVariance) return variance;
	hasRequiredVariance = 1;

	Object.defineProperty(variance, "__esModule", {
	  value: true
	});
	variance.createVariance = void 0;
	var _collection = requireCollection();
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _improveErrorMessage = requireImproveErrorMessage();
	const DEFAULT_NORMALIZATION = 'unbiased';
	const name = 'variance';
	const dependencies = ['typed', 'add', 'subtract', 'multiply', 'divide', 'mapSlices', 'isNaN'];
	variance.createVariance = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    subtract,
	    multiply,
	    divide,
	    mapSlices,
	    isNaN: mathIsNaN
	  } = _ref;
	  /**
	   * Compute the variance of a matrix or a  list with values.
	   * In case of a multidimensional array or matrix, the variance over all
	   * elements will be calculated.
	   *
	   * Additionally, it is possible to compute the variance along the rows
	   * or columns of a matrix by specifying the dimension as the second argument.
	   *
	   * Optionally, the type of normalization can be specified as the final
	   * parameter. The parameter `normalization` can be one of the following values:
	   *
	   * - 'unbiased' (default) The sum of squared errors is divided by (n - 1)
	   * - 'uncorrected'        The sum of squared errors is divided by n
	   * - 'biased'             The sum of squared errors is divided by (n + 1)
	   *
	   *
	   * Note that older browser may not like the variable name `var`. In that
	   * case, the function can be called as `math['var'](...)` instead of
	   * `math.var(...)`.
	   *
	   * Syntax:
	   *
	   *     math.variance(a, b, c, ...)
	   *     math.variance(A)
	   *     math.variance(A, normalization)
	   *     math.variance(A, dimension)
	   *     math.variance(A, dimension, normalization)
	   *
	   * Examples:
	   *
	   *     math.variance(2, 4, 6)                     // returns 4
	   *     math.variance([2, 4, 6, 8])                // returns 6.666666666666667
	   *     math.variance([2, 4, 6, 8], 'uncorrected') // returns 5
	   *     math.variance([2, 4, 6, 8], 'biased')      // returns 4
	   *
	   *     math.variance([[1, 2, 3], [4, 5, 6]])      // returns 3.5
	   *     math.variance([[1, 2, 3], [4, 6, 8]], 0)   // returns [4.5, 8, 12.5]
	   *     math.variance([[1, 2, 3], [4, 6, 8]], 1)   // returns [1, 4]
	   *     math.variance([[1, 2, 3], [4, 6, 8]], 1, 'biased') // returns [0.5, 2]
	   *
	   * See also:
	   *
	   *    mean, median, max, min, prod, std, sum
	   *
	   * @param {Array | Matrix} array
	   *                        A single matrix or or multiple scalar values
	   * @param {string} [normalization='unbiased']
	   *                        Determines how to normalize the variance.
	   *                        Choose 'unbiased' (default), 'uncorrected', or 'biased'.
	   * @param dimension {number | BigNumber}
	   *                        Determines the axis to compute the variance for a matrix
	   * @return {*} The variance
	   */
	  return typed(name, {
	    // variance([a, b, c, d, ...])
	    'Array | Matrix': function (array) {
	      return _var(array, DEFAULT_NORMALIZATION);
	    },
	    // variance([a, b, c, d, ...], normalization)
	    'Array | Matrix, string': _var,
	    // variance([a, b, c, c, ...], dim)
	    'Array | Matrix, number | BigNumber': function (array, dim) {
	      return _varDim(array, dim, DEFAULT_NORMALIZATION);
	    },
	    // variance([a, b, c, c, ...], dim, normalization)
	    'Array | Matrix, number | BigNumber, string': _varDim,
	    // variance(a, b, c, d, ...)
	    '...': function (args) {
	      return _var(args, DEFAULT_NORMALIZATION);
	    }
	  });

	  /**
	   * Recursively calculate the variance of an n-dimensional array
	   * @param {Array} array
	   * @param {string} normalization
	   *                        Determines how to normalize the variance:
	   *                        - 'unbiased'    The sum of squared errors is divided by (n - 1)
	   *                        - 'uncorrected' The sum of squared errors is divided by n
	   *                        - 'biased'      The sum of squared errors is divided by (n + 1)
	   * @return {number | BigNumber} variance
	   * @private
	   */
	  function _var(array, normalization) {
	    let sum;
	    let num = 0;
	    if (array.length === 0) {
	      throw new SyntaxError('Function variance requires one or more parameters (0 provided)');
	    }

	    // calculate the mean and number of elements
	    (0, _collection.deepForEach)(array, function (value) {
	      try {
	        sum = sum === undefined ? value : add(sum, value);
	        num++;
	      } catch (err) {
	        throw (0, _improveErrorMessage.improveErrorMessage)(err, 'variance', value);
	      }
	    });
	    if (num === 0) throw new Error('Cannot calculate variance of an empty array');
	    const mean = divide(sum, num);

	    // calculate the variance
	    sum = undefined;
	    (0, _collection.deepForEach)(array, function (value) {
	      const diff = subtract(value, mean);
	      sum = sum === undefined ? multiply(diff, diff) : add(sum, multiply(diff, diff));
	    });
	    if (mathIsNaN(sum)) {
	      return sum;
	    }
	    switch (normalization) {
	      case 'uncorrected':
	        return divide(sum, num);
	      case 'biased':
	        return divide(sum, num + 1);
	      case 'unbiased':
	        {
	          const zero = (0, _is.isBigNumber)(sum) ? sum.mul(0) : 0;
	          return num === 1 ? zero : divide(sum, num - 1);
	        }
	      default:
	        throw new Error('Unknown normalization "' + normalization + '". ' + 'Choose "unbiased" (default), "uncorrected", or "biased".');
	    }
	  }
	  function _varDim(array, dim, normalization) {
	    try {
	      if (array.length === 0) {
	        throw new SyntaxError('Function variance requires one or more parameters (0 provided)');
	      }
	      return mapSlices(array, dim, x => _var(x, normalization));
	    } catch (err) {
	      throw (0, _improveErrorMessage.improveErrorMessage)(err, 'variance');
	    }
	  }
	});
	return variance;
}

var quantileSeq = {};

var hasRequiredQuantileSeq;

function requireQuantileSeq () {
	if (hasRequiredQuantileSeq) return quantileSeq;
	hasRequiredQuantileSeq = 1;

	Object.defineProperty(quantileSeq, "__esModule", {
	  value: true
	});
	quantileSeq.createQuantileSeq = void 0;
	var _is = requireIs();
	var _array = requireArray();
	var _factory = requireFactory$b();
	const name = 'quantileSeq';
	const dependencies = ['typed', '?bignumber', 'add', 'subtract', 'divide', 'multiply', 'partitionSelect', 'compare', 'isInteger', 'smaller', 'smallerEq', 'larger', 'mapSlices'];
	quantileSeq.createQuantileSeq = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    bignumber,
	    add,
	    subtract,
	    divide,
	    multiply,
	    partitionSelect,
	    compare,
	    isInteger,
	    smaller,
	    smallerEq,
	    larger,
	    mapSlices
	  } = _ref;
	  /**
	   * Compute the prob order quantile of a matrix or a list with values.
	   * The sequence is sorted and the middle value is returned.
	   * Supported types of sequence values are: Number, BigNumber, Unit
	   * Supported types of probability are: Number, BigNumber
	   *
	   * In case of a multidimensional array or matrix, the prob order quantile
	   * of all elements will be calculated.
	   *
	   * Syntax:
	   *
	   *     math.quantileSeq(A, prob[, sorted])
	   *     math.quantileSeq(A, [prob1, prob2, ...][, sorted])
	   *     math.quantileSeq(A, N[, sorted])
	   *
	   * Examples:
	   *
	   *     math.quantileSeq([3, -1, 5, 7], 0.5)         // returns 4
	   *     math.quantileSeq([3, -1, 5, 7], [1/3, 2/3])  // returns [3, 5]
	   *     math.quantileSeq([3, -1, 5, 7], 2)           // returns [3, 5]
	   *     math.quantileSeq([-1, 3, 5, 7], 0.5, true)   // returns 4
	   *
	   * See also:
	   *
	   *     median, mean, min, max, sum, prod, std, variance
	   *
	   * @param {Array, Matrix} data                A single matrix or Array
	   * @param {Number, BigNumber, Array} probOrN  prob is the order of the quantile, while N is
	   *                                            the amount of evenly distributed steps of
	   *                                            probabilities; only one of these options can
	   *                                            be provided
	   * @param {Boolean} sorted=false              is data sorted in ascending order
	   * @return {Number, BigNumber, Unit, Array}   Quantile(s)
	   */
	  return typed(name, {
	    'Array | Matrix, number | BigNumber': (data, p) => _quantileSeqProbNumber(data, p, false),
	    'Array | Matrix, number | BigNumber, number': (data, prob, dim) => _quantileSeqDim(data, prob, false, dim, _quantileSeqProbNumber),
	    'Array | Matrix, number | BigNumber, boolean': _quantileSeqProbNumber,
	    'Array | Matrix, number | BigNumber, boolean, number': (data, prob, sorted, dim) => _quantileSeqDim(data, prob, sorted, dim, _quantileSeqProbNumber),
	    'Array | Matrix, Array | Matrix': (data, p) => _quantileSeqProbCollection(data, p, false),
	    'Array | Matrix, Array | Matrix, number': (data, prob, dim) => _quantileSeqDim(data, prob, false, dim, _quantileSeqProbCollection),
	    'Array | Matrix, Array | Matrix, boolean': _quantileSeqProbCollection,
	    'Array | Matrix, Array | Matrix, boolean, number': (data, prob, sorted, dim) => _quantileSeqDim(data, prob, sorted, dim, _quantileSeqProbCollection)
	  });
	  function _quantileSeqDim(data, prob, sorted, dim, fn) {
	    return mapSlices(data, dim, x => fn(x, prob, sorted));
	  }
	  function _quantileSeqProbNumber(data, probOrN, sorted) {
	    let probArr;
	    const dataArr = data.valueOf();
	    if (smaller(probOrN, 0)) {
	      throw new Error('N/prob must be non-negative');
	    }
	    if (smallerEq(probOrN, 1)) {
	      // quantileSeq([a, b, c, d, ...], prob[,sorted])
	      return (0, _is.isNumber)(probOrN) ? _quantileSeq(dataArr, probOrN, sorted) : bignumber(_quantileSeq(dataArr, probOrN, sorted));
	    }
	    if (larger(probOrN, 1)) {
	      // quantileSeq([a, b, c, d, ...], N[,sorted])
	      if (!isInteger(probOrN)) {
	        throw new Error('N must be a positive integer');
	      }

	      // largest possible Array length is 2^32-1
	      // 2^32 < 10^15, thus safe conversion guaranteed
	      if (larger(probOrN, 4294967295)) {
	        throw new Error('N must be less than or equal to 2^32-1, as that is the maximum length of an Array');
	      }
	      const nPlusOne = add(probOrN, 1);
	      probArr = [];
	      for (let i = 0; smaller(i, probOrN); i++) {
	        const prob = divide(i + 1, nPlusOne);
	        probArr.push(_quantileSeq(dataArr, prob, sorted));
	      }
	      return (0, _is.isNumber)(probOrN) ? probArr : bignumber(probArr);
	    }
	  }

	  /**
	   * Calculate the prob order quantile of an n-dimensional array.
	   *
	   * @param {Array, Matrix} array
	   * @param {Array, Matrix} prob
	   * @param {Boolean} sorted
	   * @return {Number, BigNumber, Unit} prob order quantile
	   * @private
	   */

	  function _quantileSeqProbCollection(data, probOrN, sorted) {
	    const dataArr = data.valueOf();
	    // quantileSeq([a, b, c, d, ...], [prob1, prob2, ...][,sorted])
	    const probOrNArr = probOrN.valueOf();
	    const probArr = [];
	    for (let i = 0; i < probOrNArr.length; ++i) {
	      probArr.push(_quantileSeq(dataArr, probOrNArr[i], sorted));
	    }
	    return probArr;
	  }

	  /**
	   * Calculate the prob order quantile of an n-dimensional array.
	   *
	   * @param {Array} array
	   * @param {Number, BigNumber} prob
	   * @param {Boolean} sorted
	   * @return {Number, BigNumber, Unit} prob order quantile
	   * @private
	   */
	  function _quantileSeq(array, prob, sorted) {
	    const flat = (0, _array.flatten)(array);
	    const len = flat.length;
	    if (len === 0) {
	      throw new Error('Cannot calculate quantile of an empty sequence');
	    }
	    const index = (0, _is.isNumber)(prob) ? prob * (len - 1) : prob.times(len - 1);
	    const integerPart = (0, _is.isNumber)(prob) ? Math.floor(index) : index.floor().toNumber();
	    const fracPart = (0, _is.isNumber)(prob) ? index % 1 : index.minus(integerPart);
	    if (isInteger(index)) {
	      return sorted ? flat[index] : partitionSelect(flat, (0, _is.isNumber)(prob) ? index : index.valueOf());
	    }
	    let left;
	    let right;
	    if (sorted) {
	      left = flat[integerPart];
	      right = flat[integerPart + 1];
	    } else {
	      right = partitionSelect(flat, integerPart + 1);

	      // max of partition is kth largest
	      left = flat[integerPart];
	      for (let i = 0; i < integerPart; ++i) {
	        if (compare(flat[i], left) > 0) {
	          left = flat[i];
	        }
	      }
	    }
	    // Q(prob) = (1-f)*A[floor(index)] + f*A[floor(index)+1]
	    return add(multiply(left, subtract(1, fracPart)), multiply(right, fracPart));
	  }
	});
	return quantileSeq;
}

var std = {};

var hasRequiredStd;

function requireStd () {
	if (hasRequiredStd) return std;
	hasRequiredStd = 1;

	Object.defineProperty(std, "__esModule", {
	  value: true
	});
	std.createStd = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'std';
	const dependencies = ['typed', 'map', 'sqrt', 'variance'];
	std.createStd = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    map,
	    sqrt,
	    variance
	  } = _ref;
	  /**
	   * Compute the standard deviation of a matrix or a  list with values.
	   * The standard deviations is defined as the square root of the variance:
	   * `std(A) = sqrt(variance(A))`.
	   * In case of a (multi dimensional) array or matrix, the standard deviation
	   * over all elements will be calculated by default, unless an axis is specified
	   * in which case the standard deviation will be computed along that axis.
	   *
	   * Additionally, it is possible to compute the standard deviation along the rows
	   * or columns of a matrix by specifying the dimension as the second argument.
	   *
	   * Optionally, the type of normalization can be specified as the final
	   * parameter. The parameter `normalization` can be one of the following values:
	   *
	   * - 'unbiased' (default) The sum of squared errors is divided by (n - 1)
	   * - 'uncorrected'        The sum of squared errors is divided by n
	   * - 'biased'             The sum of squared errors is divided by (n + 1)
	   *
	   *
	   * Syntax:
	   *
	   *     math.std(a, b, c, ...)
	   *     math.std(A)
	   *     math.std(A, normalization)
	   *     math.std(A, dimension)
	   *     math.std(A, dimension, normalization)
	   *
	   * Examples:
	   *
	   *     math.std(2, 4, 6)                     // returns 2
	   *     math.std([2, 4, 6, 8])                // returns 2.581988897471611
	   *     math.std([2, 4, 6, 8], 'uncorrected') // returns 2.23606797749979
	   *     math.std([2, 4, 6, 8], 'biased')      // returns 2
	   *
	   *     math.std([[1, 2, 3], [4, 5, 6]])      // returns 1.8708286933869707
	   *     math.std([[1, 2, 3], [4, 6, 8]], 0)    // returns [2.1213203435596424, 2.8284271247461903, 3.5355339059327378]
	   *     math.std([[1, 2, 3], [4, 6, 8]], 1)    // returns [1, 2]
	   *     math.std([[1, 2, 3], [4, 6, 8]], 1, 'biased') // returns [0.7071067811865476, 1.4142135623730951]
	   *
	   * See also:
	   *
	   *    mean, median, max, min, prod, sum, variance
	   *
	   * @param {Array | Matrix} array
	   *                        A single matrix or or multiple scalar values
	   * @param {string} [normalization='unbiased']
	   *                        Determines how to normalize the variance.
	   *                        Choose 'unbiased' (default), 'uncorrected', or 'biased'.
	   * @param dimension {number | BigNumber}
	   *                        Determines the axis to compute the standard deviation for a matrix
	   * @return {*} The standard deviation
	   */
	  return typed(name, {
	    // std([a, b, c, d, ...])
	    'Array | Matrix': _std,
	    // std([a, b, c, d, ...], normalization)
	    'Array | Matrix, string': _std,
	    // std([a, b, c, c, ...], dim)
	    'Array | Matrix, number | BigNumber': _std,
	    // std([a, b, c, c, ...], dim, normalization)
	    'Array | Matrix, number | BigNumber, string': _std,
	    // std(a, b, c, d, ...)
	    '...': function (args) {
	      return _std(args);
	    }
	  });
	  function _std(array, normalization) {
	    if (array.length === 0) {
	      throw new SyntaxError('Function std requires one or more parameters (0 provided)');
	    }
	    try {
	      const v = variance.apply(null, arguments);
	      if ((0, _is.isCollection)(v)) {
	        return map(v, sqrt);
	      } else {
	        return sqrt(v);
	      }
	    } catch (err) {
	      if (err instanceof TypeError && err.message.includes(' variance')) {
	        throw new TypeError(err.message.replace(' variance', ' std'));
	      } else {
	        throw err;
	      }
	    }
	  }
	});
	return std;
}

var corr = {};

var hasRequiredCorr;

function requireCorr () {
	if (hasRequiredCorr) return corr;
	hasRequiredCorr = 1;

	Object.defineProperty(corr, "__esModule", {
	  value: true
	});
	corr.createCorr = void 0;
	var _factory = requireFactory$b();
	const name = 'corr';
	const dependencies = ['typed', 'matrix', 'mean', 'sqrt', 'sum', 'add', 'subtract', 'multiply', 'pow', 'divide'];
	corr.createCorr = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    sqrt,
	    sum,
	    add,
	    subtract,
	    multiply,
	    pow,
	    divide
	  } = _ref;
	  /**
	   * Compute the correlation coefficient of a two list with values, For matrices, the matrix correlation coefficient is calculated.
	   *
	   * Syntax:
	   *
	   *     math.corr(A, B)
	   *
	   * Examples:
	   *
	   *     math.corr([1, 2, 3, 4, 5], [4, 5, 6, 7, 8])     // returns 1
	   *     math.corr([1, 2.2, 3, 4.8, 5], [4, 5.3, 6.6, 7, 8])     //returns 0.9569941688503644
	   *     math.corr([[1, 2.2, 3, 4.8, 5], [4, 5.3, 6.6, 7, 8]],[[1, 2.2, 3, 4.8, 5], [4, 5.3, 6.6, 7, 8]])   // returns [1,1]
	   *
	   * See also:
	   *
	   *     median, mean, min, max, sum, prod, std, variance
	   *
	   * @param {Array | Matrix} A The first array or matrix to compute correlation coefficient
	   * @param {Array | Matrix} B The second array or matrix to compute correlation coefficient
	   * @return {*} The correlation coefficient
	   */
	  return typed(name, {
	    'Array, Array': function (A, B) {
	      return _corr(A, B);
	    },
	    'Matrix, Matrix': function (A, B) {
	      const res = _corr(A.toArray(), B.toArray());
	      return Array.isArray(res) ? matrix(res) : res;
	    }
	  });
	  /**
	   * Calculate the correlation coefficient between two arrays or matrices.
	   * @param {Array | Matrix} A
	   * @param {Array | Matrix} B
	   * @return {*} correlation coefficient
	   * @private
	   */
	  function _corr(A, B) {
	    const correlations = [];
	    if (Array.isArray(A[0]) && Array.isArray(B[0])) {
	      if (A.length !== B.length) {
	        throw new SyntaxError('Dimension mismatch. Array A and B must have the same length.');
	      }
	      for (let i = 0; i < A.length; i++) {
	        if (A[i].length !== B[i].length) {
	          throw new SyntaxError('Dimension mismatch. Array A and B must have the same number of elements.');
	        }
	        correlations.push(correlation(A[i], B[i]));
	      }
	      return correlations;
	    } else {
	      if (A.length !== B.length) {
	        throw new SyntaxError('Dimension mismatch. Array A and B must have the same number of elements.');
	      }
	      return correlation(A, B);
	    }
	  }
	  function correlation(A, B) {
	    const n = A.length;
	    const sumX = sum(A);
	    const sumY = sum(B);
	    const sumXY = A.reduce((acc, x, index) => add(acc, multiply(x, B[index])), 0);
	    const sumXSquare = sum(A.map(x => pow(x, 2)));
	    const sumYSquare = sum(B.map(y => pow(y, 2)));
	    const numerator = subtract(multiply(n, sumXY), multiply(sumX, sumY));
	    const denominator = sqrt(multiply(subtract(multiply(n, sumXSquare), pow(sumX, 2)), subtract(multiply(n, sumYSquare), pow(sumY, 2))));
	    return divide(numerator, denominator);
	  }
	});
	return corr;
}

var bernoulli = {};

var hasRequiredBernoulli;

function requireBernoulli () {
	if (hasRequiredBernoulli) return bernoulli;
	hasRequiredBernoulli = 1;

	Object.defineProperty(bernoulli, "__esModule", {
	  value: true
	});
	bernoulli.createBernoulli = void 0;
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	const name = 'bernoulli';
	const dependencies = ['typed', 'config', 'isInteger', 'number', '?BigNumber', '?Fraction'];
	bernoulli.createBernoulli = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    number,
	    BigNumber,
	    Fraction
	  } = _ref;
	  /**
	   * Return the `n`th Bernoulli number, for positive integers `n`
	   *
	   * Syntax:
	   *
	   *     math.bernoulli(n)
	   *
	   * Examples:
	   *
	   *     math.bernoulli(1)                  // returns -0.5
	   *     // All other odd Bernoulli numbers are 0:
	   *     math.bernoulli(7)                  // returns 0
	   *     math.bernoulli(math.bignumber(6))  // value bignumber(1).div(42)
	   *     // Produces exact rationals for bigint or fraction input:
	   *     math.bernoulli(8n)                 // Fraction -1,30
	   *     math.bernoulli(math.fraction(10))  // Fraction 5,66
	   *
	   * See also:
	   *
	   *     combinations, gamma, stirlingS2
	   *
	   * @param {number | BigNumber | bigint | Fraction} n
	   *    Index of the Bernoulli number
	   * @return {number | BigNumber | Fraction}
	   *    nth Bernoulli number, of a type corresponding to the argument n
	   */

	  const numberCache = [undefined];
	  const fractionCache = [undefined];
	  let bigCache = [undefined];
	  let cachedPrecision = 50;
	  return typed(name, {
	    number: index => _bernoulli(index, n => n, numberCache, (a, b) => a + b, (a, b) => a * b, (a, b) => a / b),
	    'bigint | Fraction': index => _bernoulli(number(index), n => new Fraction(n), fractionCache, (a, b) => a.add(b), (a, b) => a.mul(b), (a, b) => a.div(b)),
	    BigNumber: index => {
	      if (config.precision !== cachedPrecision) {
	        bigCache = [undefined];
	        cachedPrecision = config.precision;
	      }
	      return _bernoulli(number(index), n => new BigNumber(n), bigCache, (a, b) => a.add(b), (a, b) => a.mul(b), (a, b) => a.div(b));
	    }
	  });
	});

	/**
	 * Underlying implementation, with all operations passed in.
	 * Parameters:
	 * 1. index: a (positive integer) number specifying which Bernoulli number
	 *    to compute.
	 * 2. promote: a function that takes an integer number and returns
	 *    the desired type for the Bernoulli number values.
	 * 3. A: a cache array of partial computation data that _bernoulli should use.
	 *    Different cache arrays should be provided for different types.
	 * 4. plus: a function that adds two values of the desired type.
	 * 5. times: a function that multiplies two values of the desired type.
	 * 6. divide: a function that divides one value of the desired type by another.
	 */
	function _bernoulli(index, promote, A, plus, times, divide) {
	  if (index < 0 || !(0, _number.isInteger)(index)) {
	    throw new RangeError('Bernoulli index must be nonnegative integer');
	  }
	  if (index === 0) return promote(1);
	  if (index === 1) return divide(promote(-1), promote(2));
	  if (index % 2 === 1) return promote(0);
	  // We proceed as in https://math.stackexchange.com/a/2844337
	  // (by no means the most efficient, but very simple to implement)
	  // A cache entry consists of a triple
	  // [cotangent coefficient a_n, prefactor, Bernouilli number B_2n]
	  const one = promote(1);
	  if (A.length === 1) {
	    A.push([divide(one, promote(-3)), divide(one, promote(-2)), divide(one, promote(6))]);
	  }
	  const half = index / 2;
	  const zero = promote(0);
	  const two = promote(2);
	  while (A.length <= half) {
	    const i = A.length; // next cotangent coefficient to compute
	    const lim = Math.floor((i + 1) / 2);
	    let a = zero;
	    for (let m = 1; m < lim; ++m) {
	      a = plus(a, times(A[m][0], A[i - m][0]));
	    }
	    a = times(a, two);
	    if (i % 2 === 0) a = plus(a, times(A[lim][0], A[lim][0]));
	    a = divide(a, promote(-(2 * i + 1)));
	    const prefactor = divide(times(A[i - 1][1], promote(-i * (2 * i - 1))), two);
	    A.push([a, prefactor, times(prefactor, a)]);
	  }
	  return A[half][2];
	}
	return bernoulli;
}

var combinations = {};

var hasRequiredCombinations;

function requireCombinations () {
	if (hasRequiredCombinations) return combinations;
	hasRequiredCombinations = 1;

	Object.defineProperty(combinations, "__esModule", {
	  value: true
	});
	combinations.createCombinations = void 0;
	var _factory = requireFactory$b();
	var _combinations = requireCombinations$2();
	const name = 'combinations';
	const dependencies = ['typed'];
	combinations.createCombinations = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the number of ways of picking `k` unordered outcomes from `n`
	   * possibilities.
	   *
	   * Combinations only takes integer arguments.
	   * The following condition must be enforced: k <= n.
	   *
	   * Syntax:
	   *
	   *     math.combinations(n, k)
	   *
	   * Examples:
	   *
	   *    math.combinations(7, 5) // returns 21
	   *
	   * See also:
	   *
	   *    combinationsWithRep, permutations, factorial
	   *
	   * @param {number | BigNumber} n    Total number of objects in the set
	   * @param {number | BigNumber} k    Number of objects in the subset
	   * @return {number | BigNumber}     Number of possible combinations.
	   */
	  return typed(name, {
	    'number, number': _combinations.combinationsNumber,
	    'BigNumber, BigNumber': function (n, k) {
	      const BigNumber = n.constructor;
	      let result, i;
	      const nMinusk = n.minus(k);
	      const one = new BigNumber(1);
	      if (!isPositiveInteger(n) || !isPositiveInteger(k)) {
	        throw new TypeError('Positive integer value expected in function combinations');
	      }
	      if (k.gt(n)) {
	        throw new TypeError('k must be less than n in function combinations');
	      }
	      result = one;
	      if (k.lt(nMinusk)) {
	        for (i = one; i.lte(nMinusk); i = i.plus(one)) {
	          result = result.times(k.plus(i)).dividedBy(i);
	        }
	      } else {
	        for (i = one; i.lte(k); i = i.plus(one)) {
	          result = result.times(nMinusk.plus(i)).dividedBy(i);
	        }
	      }
	      return result;
	    }

	    // TODO: implement support for collection in combinations
	  });
	});

	/**
	 * Test whether BigNumber n is a positive integer
	 * @param {BigNumber} n
	 * @returns {boolean} isPositiveInteger
	 */
	function isPositiveInteger(n) {
	  return n.isInteger() && n.gte(0);
	}
	return combinations;
}

var combinationsWithRep = {};

var hasRequiredCombinationsWithRep;

function requireCombinationsWithRep () {
	if (hasRequiredCombinationsWithRep) return combinationsWithRep;
	hasRequiredCombinationsWithRep = 1;

	Object.defineProperty(combinationsWithRep, "__esModule", {
	  value: true
	});
	combinationsWithRep.createCombinationsWithRep = void 0;
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _product = requireProduct();
	const name = 'combinationsWithRep';
	const dependencies = ['typed'];
	combinationsWithRep.createCombinationsWithRep = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Compute the number of ways of picking `k` unordered outcomes from `n`
	   * possibilities, allowing individual outcomes to be repeated more than once.
	   *
	   * CombinationsWithRep only takes integer arguments.
	   * The following condition must be enforced: k <= n + k -1.
	   *
	   * Syntax:
	   *
	   *     math.combinationsWithRep(n, k)
	   *
	   * Examples:
	   *
	   *    math.combinationsWithRep(7, 5) // returns 462
	   *
	   * See also:
	   *
	   *    combinations, permutations, factorial
	   *
	   * @param {number | BigNumber} n    Total number of objects in the set
	   * @param {number | BigNumber} k    Number of objects in the subset
	   * @return {number | BigNumber}     Number of possible combinations with replacement.
	   */
	  return typed(name, {
	    'number, number': function (n, k) {
	      if (!(0, _number.isInteger)(n) || n < 0) {
	        throw new TypeError('Positive integer value expected in function combinationsWithRep');
	      }
	      if (!(0, _number.isInteger)(k) || k < 0) {
	        throw new TypeError('Positive integer value expected in function combinationsWithRep');
	      }
	      if (n < 1) {
	        throw new TypeError('k must be less than or equal to n + k - 1');
	      }
	      if (k < n - 1) {
	        const prodrange = (0, _product.product)(n, n + k - 1);
	        return prodrange / (0, _product.product)(1, k);
	      }
	      const prodrange = (0, _product.product)(k + 1, n + k - 1);
	      return prodrange / (0, _product.product)(1, n - 1);
	    },
	    'BigNumber, BigNumber': function (n, k) {
	      const BigNumber = n.constructor;
	      let result, i;
	      const one = new BigNumber(1);
	      const nMinusOne = n.minus(one);
	      if (!isPositiveInteger(n) || !isPositiveInteger(k)) {
	        throw new TypeError('Positive integer value expected in function combinationsWithRep');
	      }
	      if (n.lt(one)) {
	        throw new TypeError('k must be less than or equal to n + k - 1 in function combinationsWithRep');
	      }
	      result = one;
	      if (k.lt(nMinusOne)) {
	        for (i = one; i.lte(nMinusOne); i = i.plus(one)) {
	          result = result.times(k.plus(i)).dividedBy(i);
	        }
	      } else {
	        for (i = one; i.lte(k); i = i.plus(one)) {
	          result = result.times(nMinusOne.plus(i)).dividedBy(i);
	        }
	      }
	      return result;
	    }
	  });
	});

	/**
	 * Test whether BigNumber n is a positive integer
	 * @param {BigNumber} n
	 * @returns {boolean} isPositiveInteger
	 */
	function isPositiveInteger(n) {
	  return n.isInteger() && n.gte(0);
	}
	return combinationsWithRep;
}

var gamma = {};

var hasRequiredGamma;

function requireGamma () {
	if (hasRequiredGamma) return gamma;
	hasRequiredGamma = 1;

	Object.defineProperty(gamma, "__esModule", {
	  value: true
	});
	gamma.createGamma = void 0;
	var _factory = requireFactory$b();
	var _index = requireNumber$2();
	const name = 'gamma';
	const dependencies = ['typed', 'config', 'multiplyScalar', 'pow', 'BigNumber', 'Complex'];
	gamma.createGamma = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    multiplyScalar,
	    pow,
	    BigNumber,
	    Complex
	  } = _ref;
	  /**
	   * Compute the gamma function of a value using Lanczos approximation for
	   * small values, and an extended Stirling approximation for large values.
	   *
	   * To avoid confusion with the matrix Gamma function, this function does
	   * not apply to matrices.
	   *
	   * Syntax:
	   *
	   *    math.gamma(n)
	   *
	   * Examples:
	   *
	   *    math.gamma(5)       // returns 24
	   *    math.gamma(-0.5)    // returns -3.5449077018110335
	   *    math.gamma(math.i)  // returns -0.15494982830180973 - 0.49801566811835596i
	   *
	   * See also:
	   *
	   *    combinations, factorial, permutations
	   *
	   * @param {number | BigNumber | Complex} n   A real or complex number
	   * @return {number | BigNumber | Complex}    The gamma of `n`
	   */

	  function gammaComplex(n) {
	    if (n.im === 0) {
	      return (0, _index.gammaNumber)(n.re);
	    }

	    // Lanczos approximation doesn't work well with real part lower than 0.5
	    // So reflection formula is required
	    if (n.re < 0.5) {
	      // Euler's reflection formula
	      // gamma(1-z) * gamma(z) = PI / sin(PI * z)
	      // real part of Z should not be integer [sin(PI) == 0 -> 1/0 - undefined]
	      // thanks to imperfect sin implementation sin(PI * n) != 0
	      // we can safely use it anyway
	      const t = new Complex(1 - n.re, -n.im);
	      const r = new Complex(Math.PI * n.re, Math.PI * n.im);
	      return new Complex(Math.PI).div(r.sin()).div(gammaComplex(t));
	    }

	    // Lanczos approximation
	    // z -= 1
	    n = new Complex(n.re - 1, n.im);

	    // x = gammaPval[0]
	    let x = new Complex(_index.gammaP[0], 0);
	    // for (i, gammaPval) in enumerate(gammaP):
	    for (let i = 1; i < _index.gammaP.length; ++i) {
	      // x += gammaPval / (z + i)
	      const gammaPval = new Complex(_index.gammaP[i], 0);
	      x = x.add(gammaPval.div(n.add(i)));
	    }
	    // t = z + gammaG + 0.5
	    const t = new Complex(n.re + _index.gammaG + 0.5, n.im);

	    // y = sqrt(2 * pi) * t ** (z + 0.5) * exp(-t) * x
	    const twoPiSqrt = Math.sqrt(2 * Math.PI);
	    const tpow = t.pow(n.add(0.5));
	    const expt = t.neg().exp();

	    // y = [x] * [sqrt(2 * pi)] * [t ** (z + 0.5)] * [exp(-t)]
	    return x.mul(twoPiSqrt).mul(tpow).mul(expt);
	  }
	  return typed(name, {
	    number: _index.gammaNumber,
	    Complex: gammaComplex,
	    BigNumber: function (n) {
	      if (n.isInteger()) {
	        return n.isNegative() || n.isZero() ? new BigNumber(Infinity) : bigFactorial(n.minus(1));
	      }
	      if (!n.isFinite()) {
	        return new BigNumber(n.isNegative() ? NaN : Infinity);
	      }
	      throw new Error('Integer BigNumber expected');
	    }
	  });

	  /**
	   * Calculate factorial for a BigNumber
	   * @param {BigNumber} n
	   * @returns {BigNumber} Returns the factorial of n
	   */
	  function bigFactorial(n) {
	    if (n < 8) {
	      return new BigNumber([1, 1, 2, 6, 24, 120, 720, 5040][n]);
	    }
	    const precision = config.precision + (Math.log(n.toNumber()) | 0);
	    const Big = BigNumber.clone({
	      precision
	    });
	    if (n % 2 === 1) {
	      return n.times(bigFactorial(new BigNumber(n - 1)));
	    }
	    let p = n;
	    let prod = new Big(n);
	    let sum = n.toNumber();
	    while (p > 2) {
	      p -= 2;
	      sum += p;
	      prod = prod.times(sum);
	    }
	    return new BigNumber(prod.toPrecision(BigNumber.precision));
	  }
	});
	return gamma;
}

var lgamma = {};

var hasRequiredLgamma;

function requireLgamma () {
	if (hasRequiredLgamma) return lgamma;
	hasRequiredLgamma = 1;

	Object.defineProperty(lgamma, "__esModule", {
	  value: true
	});
	lgamma.createLgamma = void 0;
	var _index = requireNumber$2();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	/* eslint-disable no-loss-of-precision */

	// References
	// ----------
	// [1] Hare, "Computing the Principal Branch of log-Gamma", Journal of Algorithms, 1997.
	// [2] https://math.stackexchange.com/questions/1338753/how-do-i-calculate-values-for-gamma-function-with-complex-arguments

	const name = 'lgamma';
	const dependencies = ['Complex', 'typed'];
	lgamma.createLgamma = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Complex,
	    typed
	  } = _ref;
	  // Stirling series is non-convergent, we need to use the recurrence `lgamma(z) = lgamma(z+1) - log z` to get
	  // sufficient accuracy.
	  //
	  // These two values are copied from Scipy implementation:
	  // https://github.com/scipy/scipy/blob/v1.8.0/scipy/special/_loggamma.pxd#L37
	  const SMALL_RE = 7;
	  const SMALL_IM = 7;

	  /**
	   * The coefficients are B[2*n]/(2*n*(2*n - 1)) where B[2*n] is the (2*n)th Bernoulli number. See (1.1) in [1].
	   *
	   * If you cannot access the paper, can also get these values from the formula in [2].
	   *
	   *    1 /     12 = 0.00833333333333333333333333333333
	   *    1 /    360 = 0.00277777777777777777777777777778
	   * ...
	   * 3617 / 133400 = 0.02955065359477124183006535947712
	   */
	  const coeffs = [-2.955065359477124183e-2, 6.4102564102564102564e-3, -1.9175269175269175269e-3, 8.4175084175084175084e-4, -5.952380952380952381e-4, 7.9365079365079365079e-4, -2.7777777777777777778e-3, 8.3333333333333333333e-2];

	  /**
	   * Logarithm of the gamma function for real, positive numbers and complex numbers,
	   * using Lanczos approximation for numbers and Stirling series for complex numbers.
	   *
	   * Syntax:
	   *
	   *    math.lgamma(n)
	   *
	   * Examples:
	   *
	   *    math.lgamma(5)       // returns 3.178053830347945
	   *    math.lgamma(0)       // returns Infinity
	   *    math.lgamma(-0.5)    // returns NaN
	   *    math.lgamma(math.i)  // returns -0.6509231993018536 - 1.8724366472624294i
	   *
	   * See also:
	   *
	   *    gamma
	   *
	   * @param {number | Complex} n   A real or complex number
	   * @return {number | Complex}    The log gamma of `n`
	   */
	  return typed(name, {
	    number: _index.lgammaNumber,
	    Complex: lgammaComplex,
	    BigNumber: function () {
	      throw new Error("mathjs doesn't yet provide an implementation of the algorithm lgamma for BigNumber");
	    }
	  });
	  function lgammaComplex(n) {
	    const TWOPI = 6.2831853071795864769252842; // 2*pi
	    const LOGPI = 1.1447298858494001741434262; // log(pi)

	    const REFLECTION = 0.1;
	    if (n.isNaN()) {
	      return new Complex(NaN, NaN);
	    } else if (n.im === 0) {
	      return new Complex((0, _index.lgammaNumber)(n.re), 0);
	    } else if (n.re >= SMALL_RE || Math.abs(n.im) >= SMALL_IM) {
	      return lgammaStirling(n);
	    } else if (n.re <= REFLECTION) {
	      // Reflection formula. see Proposition 3.1 in [1]
	      const tmp = (0, _number.copysign)(TWOPI, n.im) * Math.floor(0.5 * n.re + 0.25);
	      const a = n.mul(Math.PI).sin().log();
	      const b = lgammaComplex(new Complex(1 - n.re, -n.im));
	      return new Complex(LOGPI, tmp).sub(a).sub(b);
	    } else if (n.im >= 0) {
	      return lgammaRecurrence(n);
	    } else {
	      return lgammaRecurrence(n.conjugate()).conjugate();
	    }
	  }
	  function lgammaStirling(z) {
	    // formula ref in [2]
	    // computation ref:
	    // https://github.com/scipy/scipy/blob/v1.8.0/scipy/special/_loggamma.pxd#L101

	    // left part

	    // x (log(x) - 1) + 1/2 (log(2PI) - log(x))
	    // => (x - 0.5) * log(x) - x + log(2PI) / 2
	    const leftPart = z.sub(0.5).mul(z.log()).sub(z).add(_index.lnSqrt2PI);

	    // right part

	    const rz = new Complex(1, 0).div(z);
	    const rzz = rz.div(z);
	    let a = coeffs[0];
	    let b = coeffs[1];
	    const r = 2 * rzz.re;
	    const s = rzz.re * rzz.re + rzz.im * rzz.im;
	    for (let i = 2; i < 8; i++) {
	      const tmp = b;
	      b = -s * a + coeffs[i];
	      a = r * a + tmp;
	    }
	    const rightPart = rz.mul(rzz.mul(a).add(b));

	    // plus left and right

	    return leftPart.add(rightPart);
	  }
	  function lgammaRecurrence(z) {
	    // computation ref:
	    // https://github.com/scipy/scipy/blob/v1.8.0/scipy/special/_loggamma.pxd#L78

	    let signflips = 0;
	    let sb = 0;
	    let shiftprod = z;
	    z = z.add(1);
	    while (z.re <= SMALL_RE) {
	      shiftprod = shiftprod.mul(z);
	      const nsb = shiftprod.im < 0 ? 1 : 0;
	      if (nsb !== 0 && sb === 0) signflips++;
	      sb = nsb;
	      z = z.add(1);
	    }
	    return lgammaStirling(z).sub(shiftprod.log()).sub(new Complex(0, signflips * 2 * Math.PI * 1));
	  }
	});
	return lgamma;
}

var factorial = {};

var hasRequiredFactorial;

function requireFactorial () {
	if (hasRequiredFactorial) return factorial;
	hasRequiredFactorial = 1;

	Object.defineProperty(factorial, "__esModule", {
	  value: true
	});
	factorial.createFactorial = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'factorial';
	const dependencies = ['typed', 'gamma'];
	factorial.createFactorial = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    gamma
	  } = _ref;
	  /**
	   * Compute the factorial of a value
	   *
	   * Factorial only supports an integer value as argument.
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.factorial(n)
	   *
	   * Examples:
	   *
	   *    math.factorial(5)    // returns 120
	   *    math.factorial(3)    // returns 6
	   *
	   * See also:
	   *
	   *    combinations, combinationsWithRep, gamma, permutations
	   *
	   * @param {number | BigNumber | Array | Matrix} n   An integer number
	   * @return {number | BigNumber | Array | Matrix}    The factorial of `n`
	   */
	  return typed(name, {
	    number: function (n) {
	      if (n < 0) {
	        throw new Error('Value must be non-negative');
	      }
	      return gamma(n + 1);
	    },
	    BigNumber: function (n) {
	      if (n.isNegative()) {
	        throw new Error('Value must be non-negative');
	      }
	      return gamma(n.plus(1));
	    },
	    'Array | Matrix': typed.referToSelf(self => n => (0, _collection.deepMap)(n, self))
	  });
	});
	return factorial;
}

var kldivergence = {};

var hasRequiredKldivergence;

function requireKldivergence () {
	if (hasRequiredKldivergence) return kldivergence;
	hasRequiredKldivergence = 1;

	Object.defineProperty(kldivergence, "__esModule", {
	  value: true
	});
	kldivergence.createKldivergence = void 0;
	var _factory = requireFactory$b();
	const name = 'kldivergence';
	const dependencies = ['typed', 'matrix', 'divide', 'sum', 'multiply', 'map', 'dotDivide', 'log', 'isNumeric'];
	kldivergence.createKldivergence = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    divide,
	    sum,
	    multiply,
	    map,
	    dotDivide,
	    log,
	    isNumeric
	  } = _ref;
	  /**
	     * Calculate the Kullback-Leibler (KL) divergence  between two distributions
	     *
	     * Syntax:
	     *
	     *     math.kldivergence(x, y)
	     *
	     * Examples:
	     *
	     *     math.kldivergence([0.7,0.5,0.4], [0.2,0.9,0.5])   //returns 0.24376698773121153
	     *
	     *
	     * @param  {Array | Matrix} q    First vector
	     * @param  {Array | Matrix} p    Second vector
	     * @return {number}              Returns distance between q and p
	     */
	  return typed(name, {
	    'Array, Array': function (q, p) {
	      return _kldiv(matrix(q), matrix(p));
	    },
	    'Matrix, Array': function (q, p) {
	      return _kldiv(q, matrix(p));
	    },
	    'Array, Matrix': function (q, p) {
	      return _kldiv(matrix(q), p);
	    },
	    'Matrix, Matrix': function (q, p) {
	      return _kldiv(q, p);
	    }
	  });
	  function _kldiv(q, p) {
	    const plength = p.size().length;
	    const qlength = q.size().length;
	    if (plength > 1) {
	      throw new Error('first object must be one dimensional');
	    }
	    if (qlength > 1) {
	      throw new Error('second object must be one dimensional');
	    }
	    if (plength !== qlength) {
	      throw new Error('Length of two vectors must be equal');
	    }

	    // Before calculation, apply normalization
	    const sumq = sum(q);
	    if (sumq === 0) {
	      throw new Error('Sum of elements in first object must be non zero');
	    }
	    const sump = sum(p);
	    if (sump === 0) {
	      throw new Error('Sum of elements in second object must be non zero');
	    }
	    const qnorm = divide(q, sum(q));
	    const pnorm = divide(p, sum(p));
	    const result = sum(multiply(qnorm, map(dotDivide(qnorm, pnorm), x => log(x))));
	    if (isNumeric(result)) {
	      return result;
	    } else {
	      return Number.NaN;
	    }
	  }
	});
	return kldivergence;
}

var multinomial = {};

var hasRequiredMultinomial;

function requireMultinomial () {
	if (hasRequiredMultinomial) return multinomial;
	hasRequiredMultinomial = 1;

	Object.defineProperty(multinomial, "__esModule", {
	  value: true
	});
	multinomial.createMultinomial = void 0;
	var _collection = requireCollection();
	var _factory = requireFactory$b();
	const name = 'multinomial';
	const dependencies = ['typed', 'add', 'divide', 'multiply', 'factorial', 'isInteger', 'isPositive'];
	multinomial.createMultinomial = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    divide,
	    multiply,
	    factorial,
	    isInteger,
	    isPositive
	  } = _ref;
	  /**
	   * Multinomial Coefficients compute the number of ways of picking a1, a2, ..., ai unordered outcomes from `n` possibilities.
	   *
	   * multinomial takes one array of integers as an argument.
	   * The following condition must be enforced: every ai <= 0
	   *
	   * Syntax:
	   *
	   *     math.multinomial(a) // a is an array type
	   *
	   * Examples:
	   *
	   *    math.multinomial([1,2,1]) // returns 12
	   *
	   * See also:
	   *
	   *    combinations, factorial
	   *
	   * @param {number[] | BigNumber[]} a    Integer numbers of objects in the subset
	   * @return {Number | BigNumber}         Multinomial coefficient.
	   */
	  return typed(name, {
	    'Array | Matrix': function (a) {
	      let sum = 0;
	      let denom = 1;
	      (0, _collection.deepForEach)(a, function (ai) {
	        if (!isInteger(ai) || !isPositive(ai)) {
	          throw new TypeError('Positive integer value expected in function multinomial');
	        }
	        sum = add(sum, ai);
	        denom = multiply(denom, factorial(ai));
	      });
	      return divide(factorial(sum), denom);
	    }
	  });
	});
	return multinomial;
}

var permutations = {};

var hasRequiredPermutations;

function requirePermutations () {
	if (hasRequiredPermutations) return permutations;
	hasRequiredPermutations = 1;

	Object.defineProperty(permutations, "__esModule", {
	  value: true
	});
	permutations.createPermutations = void 0;
	var _number = requireNumber$3();
	var _product = requireProduct();
	var _factory = requireFactory$b();
	const name = 'permutations';
	const dependencies = ['typed', 'factorial'];
	permutations.createPermutations = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    factorial
	  } = _ref;
	  /**
	   * Compute the number of ways of obtaining an ordered subset of `k` elements
	   * from a set of `n` elements.
	   *
	   * Permutations only takes integer arguments.
	   * The following condition must be enforced: k <= n.
	   *
	   * Syntax:
	   *
	   *     math.permutations(n)
	   *     math.permutations(n, k)
	   *
	   * Examples:
	   *
	   *    math.permutations(5)     // 120
	   *    math.permutations(5, 3)  // 60
	   *
	   * See also:
	   *
	   *    combinations, combinationsWithRep, factorial
	   *
	   * @param {number | BigNumber} n   The number of objects in total
	   * @param {number | BigNumber} [k] The number of objects in the subset
	   * @return {number | BigNumber}    The number of permutations
	   */
	  return typed(name, {
	    'number | BigNumber': factorial,
	    'number, number': function (n, k) {
	      if (!(0, _number.isInteger)(n) || n < 0) {
	        throw new TypeError('Positive integer value expected in function permutations');
	      }
	      if (!(0, _number.isInteger)(k) || k < 0) {
	        throw new TypeError('Positive integer value expected in function permutations');
	      }
	      if (k > n) {
	        throw new TypeError('second argument k must be less than or equal to first argument n');
	      }
	      // Permute n objects, k at a time
	      return (0, _product.product)(n - k + 1, n);
	    },
	    'BigNumber, BigNumber': function (n, k) {
	      let result, i;
	      if (!isPositiveInteger(n) || !isPositiveInteger(k)) {
	        throw new TypeError('Positive integer value expected in function permutations');
	      }
	      if (k.gt(n)) {
	        throw new TypeError('second argument k must be less than or equal to first argument n');
	      }
	      const one = n.mul(0).add(1);
	      result = one;
	      for (i = n.minus(k).plus(1); i.lte(n); i = i.plus(1)) {
	        result = result.times(i);
	      }
	      return result;
	    }

	    // TODO: implement support for collection in permutations
	  });
	});

	/**
	 * Test whether BigNumber n is a positive integer
	 * @param {BigNumber} n
	 * @returns {boolean} isPositiveInteger
	 */
	function isPositiveInteger(n) {
	  return n.isInteger() && n.gte(0);
	}
	return permutations;
}

var pickRandom = {};

var seededRNG = {};

var alea$1 = {exports: {}};

var alea = alea$1.exports;

var hasRequiredAlea;

function requireAlea () {
	if (hasRequiredAlea) return alea$1.exports;
	hasRequiredAlea = 1;
	(function (module) {
		// A port of an algorithm by Johannes Baagøe <baagoe@baagoe.com>, 2010
		// http://baagoe.com/en/RandomMusings/javascript/
		// https://github.com/nquinlan/better-random-numbers-for-javascript-mirror
		// Original work is under MIT license -

		// Copyright (C) 2010 by Johannes Baagøe <baagoe@baagoe.org>
		//
		// Permission is hereby granted, free of charge, to any person obtaining a copy
		// of this software and associated documentation files (the "Software"), to deal
		// in the Software without restriction, including without limitation the rights
		// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
		// copies of the Software, and to permit persons to whom the Software is
		// furnished to do so, subject to the following conditions:
		//
		// The above copyright notice and this permission notice shall be included in
		// all copies or substantial portions of the Software.
		//
		// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
		// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
		// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
		// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
		// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
		// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
		// THE SOFTWARE.



		(function(global, module, define) {

		function Alea(seed) {
		  var me = this, mash = Mash();

		  me.next = function() {
		    var t = 2091639 * me.s0 + me.c * 2.3283064365386963e-10; // 2^-32
		    me.s0 = me.s1;
		    me.s1 = me.s2;
		    return me.s2 = t - (me.c = t | 0);
		  };

		  // Apply the seeding algorithm from Baagoe.
		  me.c = 1;
		  me.s0 = mash(' ');
		  me.s1 = mash(' ');
		  me.s2 = mash(' ');
		  me.s0 -= mash(seed);
		  if (me.s0 < 0) { me.s0 += 1; }
		  me.s1 -= mash(seed);
		  if (me.s1 < 0) { me.s1 += 1; }
		  me.s2 -= mash(seed);
		  if (me.s2 < 0) { me.s2 += 1; }
		  mash = null;
		}

		function copy(f, t) {
		  t.c = f.c;
		  t.s0 = f.s0;
		  t.s1 = f.s1;
		  t.s2 = f.s2;
		  return t;
		}

		function impl(seed, opts) {
		  var xg = new Alea(seed),
		      state = opts && opts.state,
		      prng = xg.next;
		  prng.int32 = function() { return (xg.next() * 0x100000000) | 0; };
		  prng.double = function() {
		    return prng() + (prng() * 0x200000 | 0) * 1.1102230246251565e-16; // 2^-53
		  };
		  prng.quick = prng;
		  if (state) {
		    if (typeof(state) == 'object') copy(state, xg);
		    prng.state = function() { return copy(xg, {}); };
		  }
		  return prng;
		}

		function Mash() {
		  var n = 0xefc8249d;

		  var mash = function(data) {
		    data = String(data);
		    for (var i = 0; i < data.length; i++) {
		      n += data.charCodeAt(i);
		      var h = 0.02519603282416938 * n;
		      n = h >>> 0;
		      h -= n;
		      h *= n;
		      n = h >>> 0;
		      h -= n;
		      n += h * 0x100000000; // 2^32
		    }
		    return (n >>> 0) * 2.3283064365386963e-10; // 2^-32
		  };

		  return mash;
		}


		if (module && module.exports) {
		  module.exports = impl;
		} else if (define && define.amd) {
		  define(function() { return impl; });
		} else {
		  this.alea = impl;
		}

		})(
		  alea,
		  module,    // present in node.js
		  (typeof undefined) == 'function'    // present with an AMD loader
		); 
	} (alea$1));
	return alea$1.exports;
}

var xor128$1 = {exports: {}};

var xor128 = xor128$1.exports;

var hasRequiredXor128;

function requireXor128 () {
	if (hasRequiredXor128) return xor128$1.exports;
	hasRequiredXor128 = 1;
	(function (module) {
		// A Javascript implementaion of the "xor128" prng algorithm by
		// George Marsaglia.  See http://www.jstatsoft.org/v08/i14/paper

		(function(global, module, define) {

		function XorGen(seed) {
		  var me = this, strseed = '';

		  me.x = 0;
		  me.y = 0;
		  me.z = 0;
		  me.w = 0;

		  // Set up generator function.
		  me.next = function() {
		    var t = me.x ^ (me.x << 11);
		    me.x = me.y;
		    me.y = me.z;
		    me.z = me.w;
		    return me.w ^= (me.w >>> 19) ^ t ^ (t >>> 8);
		  };

		  if (seed === (seed | 0)) {
		    // Integer seed.
		    me.x = seed;
		  } else {
		    // String seed.
		    strseed += seed;
		  }

		  // Mix in string seed, then discard an initial batch of 64 values.
		  for (var k = 0; k < strseed.length + 64; k++) {
		    me.x ^= strseed.charCodeAt(k) | 0;
		    me.next();
		  }
		}

		function copy(f, t) {
		  t.x = f.x;
		  t.y = f.y;
		  t.z = f.z;
		  t.w = f.w;
		  return t;
		}

		function impl(seed, opts) {
		  var xg = new XorGen(seed),
		      state = opts && opts.state,
		      prng = function() { return (xg.next() >>> 0) / 0x100000000; };
		  prng.double = function() {
		    do {
		      var top = xg.next() >>> 11,
		          bot = (xg.next() >>> 0) / 0x100000000,
		          result = (top + bot) / (1 << 21);
		    } while (result === 0);
		    return result;
		  };
		  prng.int32 = xg.next;
		  prng.quick = prng;
		  if (state) {
		    if (typeof(state) == 'object') copy(state, xg);
		    prng.state = function() { return copy(xg, {}); };
		  }
		  return prng;
		}

		if (module && module.exports) {
		  module.exports = impl;
		} else if (define && define.amd) {
		  define(function() { return impl; });
		} else {
		  this.xor128 = impl;
		}

		})(
		  xor128,
		  module,    // present in node.js
		  (typeof undefined) == 'function'    // present with an AMD loader
		); 
	} (xor128$1));
	return xor128$1.exports;
}

var xorwow$1 = {exports: {}};

var xorwow = xorwow$1.exports;

var hasRequiredXorwow;

function requireXorwow () {
	if (hasRequiredXorwow) return xorwow$1.exports;
	hasRequiredXorwow = 1;
	(function (module) {
		// A Javascript implementaion of the "xorwow" prng algorithm by
		// George Marsaglia.  See http://www.jstatsoft.org/v08/i14/paper

		(function(global, module, define) {

		function XorGen(seed) {
		  var me = this, strseed = '';

		  // Set up generator function.
		  me.next = function() {
		    var t = (me.x ^ (me.x >>> 2));
		    me.x = me.y; me.y = me.z; me.z = me.w; me.w = me.v;
		    return (me.d = (me.d + 362437 | 0)) +
		       (me.v = (me.v ^ (me.v << 4)) ^ (t ^ (t << 1))) | 0;
		  };

		  me.x = 0;
		  me.y = 0;
		  me.z = 0;
		  me.w = 0;
		  me.v = 0;

		  if (seed === (seed | 0)) {
		    // Integer seed.
		    me.x = seed;
		  } else {
		    // String seed.
		    strseed += seed;
		  }

		  // Mix in string seed, then discard an initial batch of 64 values.
		  for (var k = 0; k < strseed.length + 64; k++) {
		    me.x ^= strseed.charCodeAt(k) | 0;
		    if (k == strseed.length) {
		      me.d = me.x << 10 ^ me.x >>> 4;
		    }
		    me.next();
		  }
		}

		function copy(f, t) {
		  t.x = f.x;
		  t.y = f.y;
		  t.z = f.z;
		  t.w = f.w;
		  t.v = f.v;
		  t.d = f.d;
		  return t;
		}

		function impl(seed, opts) {
		  var xg = new XorGen(seed),
		      state = opts && opts.state,
		      prng = function() { return (xg.next() >>> 0) / 0x100000000; };
		  prng.double = function() {
		    do {
		      var top = xg.next() >>> 11,
		          bot = (xg.next() >>> 0) / 0x100000000,
		          result = (top + bot) / (1 << 21);
		    } while (result === 0);
		    return result;
		  };
		  prng.int32 = xg.next;
		  prng.quick = prng;
		  if (state) {
		    if (typeof(state) == 'object') copy(state, xg);
		    prng.state = function() { return copy(xg, {}); };
		  }
		  return prng;
		}

		if (module && module.exports) {
		  module.exports = impl;
		} else if (define && define.amd) {
		  define(function() { return impl; });
		} else {
		  this.xorwow = impl;
		}

		})(
		  xorwow,
		  module,    // present in node.js
		  (typeof undefined) == 'function'    // present with an AMD loader
		); 
	} (xorwow$1));
	return xorwow$1.exports;
}

var xorshift7$1 = {exports: {}};

var xorshift7 = xorshift7$1.exports;

var hasRequiredXorshift7;

function requireXorshift7 () {
	if (hasRequiredXorshift7) return xorshift7$1.exports;
	hasRequiredXorshift7 = 1;
	(function (module) {
		// A Javascript implementaion of the "xorshift7" algorithm by
		// François Panneton and Pierre L'ecuyer:
		// "On the Xorgshift Random Number Generators"
		// http://saluc.engr.uconn.edu/refs/crypto/rng/panneton05onthexorshift.pdf

		(function(global, module, define) {

		function XorGen(seed) {
		  var me = this;

		  // Set up generator function.
		  me.next = function() {
		    // Update xor generator.
		    var X = me.x, i = me.i, t, v;
		    t = X[i]; t ^= (t >>> 7); v = t ^ (t << 24);
		    t = X[(i + 1) & 7]; v ^= t ^ (t >>> 10);
		    t = X[(i + 3) & 7]; v ^= t ^ (t >>> 3);
		    t = X[(i + 4) & 7]; v ^= t ^ (t << 7);
		    t = X[(i + 7) & 7]; t = t ^ (t << 13); v ^= t ^ (t << 9);
		    X[i] = v;
		    me.i = (i + 1) & 7;
		    return v;
		  };

		  function init(me, seed) {
		    var j, X = [];

		    if (seed === (seed | 0)) {
		      // Seed state array using a 32-bit integer.
		      X[0] = seed;
		    } else {
		      // Seed state using a string.
		      seed = '' + seed;
		      for (j = 0; j < seed.length; ++j) {
		        X[j & 7] = (X[j & 7] << 15) ^
		            (seed.charCodeAt(j) + X[(j + 1) & 7] << 13);
		      }
		    }
		    // Enforce an array length of 8, not all zeroes.
		    while (X.length < 8) X.push(0);
		    for (j = 0; j < 8 && X[j] === 0; ++j);
		    if (j == 8) X[7] = -1; else X[j];

		    me.x = X;
		    me.i = 0;

		    // Discard an initial 256 values.
		    for (j = 256; j > 0; --j) {
		      me.next();
		    }
		  }

		  init(me, seed);
		}

		function copy(f, t) {
		  t.x = f.x.slice();
		  t.i = f.i;
		  return t;
		}

		function impl(seed, opts) {
		  if (seed == null) seed = +(new Date);
		  var xg = new XorGen(seed),
		      state = opts && opts.state,
		      prng = function() { return (xg.next() >>> 0) / 0x100000000; };
		  prng.double = function() {
		    do {
		      var top = xg.next() >>> 11,
		          bot = (xg.next() >>> 0) / 0x100000000,
		          result = (top + bot) / (1 << 21);
		    } while (result === 0);
		    return result;
		  };
		  prng.int32 = xg.next;
		  prng.quick = prng;
		  if (state) {
		    if (state.x) copy(state, xg);
		    prng.state = function() { return copy(xg, {}); };
		  }
		  return prng;
		}

		if (module && module.exports) {
		  module.exports = impl;
		} else if (define && define.amd) {
		  define(function() { return impl; });
		} else {
		  this.xorshift7 = impl;
		}

		})(
		  xorshift7,
		  module,    // present in node.js
		  (typeof undefined) == 'function'    // present with an AMD loader
		); 
	} (xorshift7$1));
	return xorshift7$1.exports;
}

var xor4096$1 = {exports: {}};

var xor4096 = xor4096$1.exports;

var hasRequiredXor4096;

function requireXor4096 () {
	if (hasRequiredXor4096) return xor4096$1.exports;
	hasRequiredXor4096 = 1;
	(function (module) {
		// A Javascript implementaion of Richard Brent's Xorgens xor4096 algorithm.
		//
		// This fast non-cryptographic random number generator is designed for
		// use in Monte-Carlo algorithms. It combines a long-period xorshift
		// generator with a Weyl generator, and it passes all common batteries
		// of stasticial tests for randomness while consuming only a few nanoseconds
		// for each prng generated.  For background on the generator, see Brent's
		// paper: "Some long-period random number generators using shifts and xors."
		// http://arxiv.org/pdf/1004.3115v1.pdf
		//
		// Usage:
		//
		// var xor4096 = require('xor4096');
		// random = xor4096(1);                        // Seed with int32 or string.
		// assert.equal(random(), 0.1520436450538547); // (0, 1) range, 53 bits.
		// assert.equal(random.int32(), 1806534897);   // signed int32, 32 bits.
		//
		// For nonzero numeric keys, this impelementation provides a sequence
		// identical to that by Brent's xorgens 3 implementaion in C.  This
		// implementation also provides for initalizing the generator with
		// string seeds, or for saving and restoring the state of the generator.
		//
		// On Chrome, this prng benchmarks about 2.1 times slower than
		// Javascript's built-in Math.random().

		(function(global, module, define) {

		function XorGen(seed) {
		  var me = this;

		  // Set up generator function.
		  me.next = function() {
		    var w = me.w,
		        X = me.X, i = me.i, t, v;
		    // Update Weyl generator.
		    me.w = w = (w + 0x61c88647) | 0;
		    // Update xor generator.
		    v = X[(i + 34) & 127];
		    t = X[i = ((i + 1) & 127)];
		    v ^= v << 13;
		    t ^= t << 17;
		    v ^= v >>> 15;
		    t ^= t >>> 12;
		    // Update Xor generator array state.
		    v = X[i] = v ^ t;
		    me.i = i;
		    // Result is the combination.
		    return (v + (w ^ (w >>> 16))) | 0;
		  };

		  function init(me, seed) {
		    var t, v, i, j, w, X = [], limit = 128;
		    if (seed === (seed | 0)) {
		      // Numeric seeds initialize v, which is used to generates X.
		      v = seed;
		      seed = null;
		    } else {
		      // String seeds are mixed into v and X one character at a time.
		      seed = seed + '\0';
		      v = 0;
		      limit = Math.max(limit, seed.length);
		    }
		    // Initialize circular array and weyl value.
		    for (i = 0, j = -32; j < limit; ++j) {
		      // Put the unicode characters into the array, and shuffle them.
		      if (seed) v ^= seed.charCodeAt((j + 32) % seed.length);
		      // After 32 shuffles, take v as the starting w value.
		      if (j === 0) w = v;
		      v ^= v << 10;
		      v ^= v >>> 15;
		      v ^= v << 4;
		      v ^= v >>> 13;
		      if (j >= 0) {
		        w = (w + 0x61c88647) | 0;     // Weyl.
		        t = (X[j & 127] ^= (v + w));  // Combine xor and weyl to init array.
		        i = (0 == t) ? i + 1 : 0;     // Count zeroes.
		      }
		    }
		    // We have detected all zeroes; make the key nonzero.
		    if (i >= 128) {
		      X[(seed && seed.length || 0) & 127] = -1;
		    }
		    // Run the generator 512 times to further mix the state before using it.
		    // Factoring this as a function slows the main generator, so it is just
		    // unrolled here.  The weyl generator is not advanced while warming up.
		    i = 127;
		    for (j = 4 * 128; j > 0; --j) {
		      v = X[(i + 34) & 127];
		      t = X[i = ((i + 1) & 127)];
		      v ^= v << 13;
		      t ^= t << 17;
		      v ^= v >>> 15;
		      t ^= t >>> 12;
		      X[i] = v ^ t;
		    }
		    // Storing state as object members is faster than using closure variables.
		    me.w = w;
		    me.X = X;
		    me.i = i;
		  }

		  init(me, seed);
		}

		function copy(f, t) {
		  t.i = f.i;
		  t.w = f.w;
		  t.X = f.X.slice();
		  return t;
		}
		function impl(seed, opts) {
		  if (seed == null) seed = +(new Date);
		  var xg = new XorGen(seed),
		      state = opts && opts.state,
		      prng = function() { return (xg.next() >>> 0) / 0x100000000; };
		  prng.double = function() {
		    do {
		      var top = xg.next() >>> 11,
		          bot = (xg.next() >>> 0) / 0x100000000,
		          result = (top + bot) / (1 << 21);
		    } while (result === 0);
		    return result;
		  };
		  prng.int32 = xg.next;
		  prng.quick = prng;
		  if (state) {
		    if (state.X) copy(state, xg);
		    prng.state = function() { return copy(xg, {}); };
		  }
		  return prng;
		}

		if (module && module.exports) {
		  module.exports = impl;
		} else if (define && define.amd) {
		  define(function() { return impl; });
		} else {
		  this.xor4096 = impl;
		}

		})(
		  xor4096,                                     // window object or global
		  module,    // present in node.js
		  (typeof undefined) == 'function'    // present with an AMD loader
		); 
	} (xor4096$1));
	return xor4096$1.exports;
}

var tychei$1 = {exports: {}};

var tychei = tychei$1.exports;

var hasRequiredTychei;

function requireTychei () {
	if (hasRequiredTychei) return tychei$1.exports;
	hasRequiredTychei = 1;
	(function (module) {
		// A Javascript implementaion of the "Tyche-i" prng algorithm by
		// Samuel Neves and Filipe Araujo.
		// See https://eden.dei.uc.pt/~sneves/pubs/2011-snfa2.pdf

		(function(global, module, define) {

		function XorGen(seed) {
		  var me = this, strseed = '';

		  // Set up generator function.
		  me.next = function() {
		    var b = me.b, c = me.c, d = me.d, a = me.a;
		    b = (b << 25) ^ (b >>> 7) ^ c;
		    c = (c - d) | 0;
		    d = (d << 24) ^ (d >>> 8) ^ a;
		    a = (a - b) | 0;
		    me.b = b = (b << 20) ^ (b >>> 12) ^ c;
		    me.c = c = (c - d) | 0;
		    me.d = (d << 16) ^ (c >>> 16) ^ a;
		    return me.a = (a - b) | 0;
		  };

		  /* The following is non-inverted tyche, which has better internal
		   * bit diffusion, but which is about 25% slower than tyche-i in JS.
		  me.next = function() {
		    var a = me.a, b = me.b, c = me.c, d = me.d;
		    a = (me.a + me.b | 0) >>> 0;
		    d = me.d ^ a; d = d << 16 ^ d >>> 16;
		    c = me.c + d | 0;
		    b = me.b ^ c; b = b << 12 ^ d >>> 20;
		    me.a = a = a + b | 0;
		    d = d ^ a; me.d = d = d << 8 ^ d >>> 24;
		    me.c = c = c + d | 0;
		    b = b ^ c;
		    return me.b = (b << 7 ^ b >>> 25);
		  }
		  */

		  me.a = 0;
		  me.b = 0;
		  me.c = 2654435769 | 0;
		  me.d = 1367130551;

		  if (seed === Math.floor(seed)) {
		    // Integer seed.
		    me.a = (seed / 0x100000000) | 0;
		    me.b = seed | 0;
		  } else {
		    // String seed.
		    strseed += seed;
		  }

		  // Mix in string seed, then discard an initial batch of 64 values.
		  for (var k = 0; k < strseed.length + 20; k++) {
		    me.b ^= strseed.charCodeAt(k) | 0;
		    me.next();
		  }
		}

		function copy(f, t) {
		  t.a = f.a;
		  t.b = f.b;
		  t.c = f.c;
		  t.d = f.d;
		  return t;
		}
		function impl(seed, opts) {
		  var xg = new XorGen(seed),
		      state = opts && opts.state,
		      prng = function() { return (xg.next() >>> 0) / 0x100000000; };
		  prng.double = function() {
		    do {
		      var top = xg.next() >>> 11,
		          bot = (xg.next() >>> 0) / 0x100000000,
		          result = (top + bot) / (1 << 21);
		    } while (result === 0);
		    return result;
		  };
		  prng.int32 = xg.next;
		  prng.quick = prng;
		  if (state) {
		    if (typeof(state) == 'object') copy(state, xg);
		    prng.state = function() { return copy(xg, {}); };
		  }
		  return prng;
		}

		if (module && module.exports) {
		  module.exports = impl;
		} else if (define && define.amd) {
		  define(function() { return impl; });
		} else {
		  this.tychei = impl;
		}

		})(
		  tychei,
		  module,    // present in node.js
		  (typeof undefined) == 'function'    // present with an AMD loader
		); 
	} (tychei$1));
	return tychei$1.exports;
}

var seedrandom$2 = {exports: {}};

var empty = {};

var empty$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': empty
});

var require$$0$2 = /*@__PURE__*/getAugmentedNamespace(empty$1);

/*
Copyright 2019 David Bau.

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

*/
var seedrandom$1 = seedrandom$2.exports;

var hasRequiredSeedrandom$1;

function requireSeedrandom$1 () {
	if (hasRequiredSeedrandom$1) return seedrandom$2.exports;
	hasRequiredSeedrandom$1 = 1;
	(function (module) {
		(function (global, pool, math) {
		//
		// The following constants are related to IEEE 754 limits.
		//

		var width = 256,        // each RC4 output is 0 <= x < 256
		    chunks = 6,         // at least six RC4 outputs for each double
		    digits = 52,        // there are 52 significant digits in a double
		    rngname = 'random', // rngname: name for Math.random and Math.seedrandom
		    startdenom = math.pow(width, chunks),
		    significance = math.pow(2, digits),
		    overflow = significance * 2,
		    mask = width - 1,
		    nodecrypto;         // node.js crypto module, initialized at the bottom.

		//
		// seedrandom()
		// This is the seedrandom function described above.
		//
		function seedrandom(seed, options, callback) {
		  var key = [];
		  options = (options == true) ? { entropy: true } : (options || {});

		  // Flatten the seed string or build one from local entropy if needed.
		  var shortseed = mixkey(flatten(
		    options.entropy ? [seed, tostring(pool)] :
		    (seed == null) ? autoseed() : seed, 3), key);

		  // Use the seed to initialize an ARC4 generator.
		  var arc4 = new ARC4(key);

		  // This function returns a random double in [0, 1) that contains
		  // randomness in every bit of the mantissa of the IEEE 754 value.
		  var prng = function() {
		    var n = arc4.g(chunks),             // Start with a numerator n < 2 ^ 48
		        d = startdenom,                 //   and denominator d = 2 ^ 48.
		        x = 0;                          //   and no 'extra last byte'.
		    while (n < significance) {          // Fill up all significant digits by
		      n = (n + x) * width;              //   shifting numerator and
		      d *= width;                       //   denominator and generating a
		      x = arc4.g(1);                    //   new least-significant-byte.
		    }
		    while (n >= overflow) {             // To avoid rounding up, before adding
		      n /= 2;                           //   last byte, shift everything
		      d /= 2;                           //   right using integer math until
		      x >>>= 1;                         //   we have exactly the desired bits.
		    }
		    return (n + x) / d;                 // Form the number within [0, 1).
		  };

		  prng.int32 = function() { return arc4.g(4) | 0; };
		  prng.quick = function() { return arc4.g(4) / 0x100000000; };
		  prng.double = prng;

		  // Mix the randomness into accumulated entropy.
		  mixkey(tostring(arc4.S), pool);

		  // Calling convention: what to return as a function of prng, seed, is_math.
		  return (options.pass || callback ||
		      function(prng, seed, is_math_call, state) {
		        if (state) {
		          // Load the arc4 state from the given state if it has an S array.
		          if (state.S) { copy(state, arc4); }
		          // Only provide the .state method if requested via options.state.
		          prng.state = function() { return copy(arc4, {}); };
		        }

		        // If called as a method of Math (Math.seedrandom()), mutate
		        // Math.random because that is how seedrandom.js has worked since v1.0.
		        if (is_math_call) { math[rngname] = prng; return seed; }

		        // Otherwise, it is a newer calling convention, so return the
		        // prng directly.
		        else return prng;
		      })(
		  prng,
		  shortseed,
		  'global' in options ? options.global : (this == math),
		  options.state);
		}

		//
		// ARC4
		//
		// An ARC4 implementation.  The constructor takes a key in the form of
		// an array of at most (width) integers that should be 0 <= x < (width).
		//
		// The g(count) method returns a pseudorandom integer that concatenates
		// the next (count) outputs from ARC4.  Its return value is a number x
		// that is in the range 0 <= x < (width ^ count).
		//
		function ARC4(key) {
		  var t, keylen = key.length,
		      me = this, i = 0, j = me.i = me.j = 0, s = me.S = [];

		  // The empty key [] is treated as [0].
		  if (!keylen) { key = [keylen++]; }

		  // Set up S using the standard key scheduling algorithm.
		  while (i < width) {
		    s[i] = i++;
		  }
		  for (i = 0; i < width; i++) {
		    s[i] = s[j = mask & (j + key[i % keylen] + (t = s[i]))];
		    s[j] = t;
		  }

		  // The "g" method returns the next (count) outputs as one number.
		  (me.g = function(count) {
		    // Using instance members instead of closure state nearly doubles speed.
		    var t, r = 0,
		        i = me.i, j = me.j, s = me.S;
		    while (count--) {
		      t = s[i = mask & (i + 1)];
		      r = r * width + s[mask & ((s[i] = s[j = mask & (j + t)]) + (s[j] = t))];
		    }
		    me.i = i; me.j = j;
		    return r;
		    // For robust unpredictability, the function call below automatically
		    // discards an initial batch of values.  This is called RC4-drop[256].
		    // See http://google.com/search?q=rsa+fluhrer+response&btnI
		  })(width);
		}

		//
		// copy()
		// Copies internal state of ARC4 to or from a plain object.
		//
		function copy(f, t) {
		  t.i = f.i;
		  t.j = f.j;
		  t.S = f.S.slice();
		  return t;
		}
		//
		// flatten()
		// Converts an object tree to nested arrays of strings.
		//
		function flatten(obj, depth) {
		  var result = [], typ = (typeof obj), prop;
		  if (depth && typ == 'object') {
		    for (prop in obj) {
		      try { result.push(flatten(obj[prop], depth - 1)); } catch (e) {}
		    }
		  }
		  return (result.length ? result : typ == 'string' ? obj : obj + '\0');
		}

		//
		// mixkey()
		// Mixes a string seed into a key that is an array of integers, and
		// returns a shortened string seed that is equivalent to the result key.
		//
		function mixkey(seed, key) {
		  var stringseed = seed + '', smear, j = 0;
		  while (j < stringseed.length) {
		    key[mask & j] =
		      mask & ((smear ^= key[mask & j] * 19) + stringseed.charCodeAt(j++));
		  }
		  return tostring(key);
		}

		//
		// autoseed()
		// Returns an object for autoseeding, using window.crypto and Node crypto
		// module if available.
		//
		function autoseed() {
		  try {
		    var out;
		    if (nodecrypto && (out = nodecrypto.randomBytes)) {
		      // The use of 'out' to remember randomBytes makes tight minified code.
		      out = out(width);
		    } else {
		      out = new Uint8Array(width);
		      (global.crypto || global.msCrypto).getRandomValues(out);
		    }
		    return tostring(out);
		  } catch (e) {
		    var browser = global.navigator,
		        plugins = browser && browser.plugins;
		    return [+new Date, global, plugins, global.screen, tostring(pool)];
		  }
		}

		//
		// tostring()
		// Converts an array of charcodes to a string
		//
		function tostring(a) {
		  return String.fromCharCode.apply(0, a);
		}

		//
		// When seedrandom.js is loaded, we immediately mix a few bits
		// from the built-in RNG into the entropy pool.  Because we do
		// not want to interfere with deterministic PRNG state later,
		// seedrandom will not call math.random on its own again after
		// initialization.
		//
		mixkey(math.random(), pool);

		//
		// Nodejs and AMD support: export the implementation as a module using
		// either convention.
		//
		if (module.exports) {
		  module.exports = seedrandom;
		  // When in node.js, try using crypto package for autoseeding.
		  try {
		    nodecrypto = require$$0$2;
		  } catch (ex) {}
		} else {
		  // When included as a plain script, set up Math.seedrandom global.
		  math['seed' + rngname] = seedrandom;
		}


		// End anonymous scope, and pass initial values.
		})(
		  // global: `self` in browsers (including strict mode and web workers),
		  // otherwise `this` in Node and other environments
		  (typeof self !== 'undefined') ? self : seedrandom$1,
		  [],     // pool: entropy pool starts empty
		  Math    // math: package containing random, pow, and seedrandom
		); 
	} (seedrandom$2));
	return seedrandom$2.exports;
}

var seedrandom;
var hasRequiredSeedrandom;

function requireSeedrandom () {
	if (hasRequiredSeedrandom) return seedrandom;
	hasRequiredSeedrandom = 1;
	// A library of seedable RNGs implemented in Javascript.
	//
	// Usage:
	//
	// var seedrandom = require('seedrandom');
	// var random = seedrandom(1); // or any seed.
	// var x = random();       // 0 <= x < 1.  Every bit is random.
	// var x = random.quick(); // 0 <= x < 1.  32 bits of randomness.

	// alea, a 53-bit multiply-with-carry generator by Johannes Baagøe.
	// Period: ~2^116
	// Reported to pass all BigCrush tests.
	var alea = requireAlea();

	// xor128, a pure xor-shift generator by George Marsaglia.
	// Period: 2^128-1.
	// Reported to fail: MatrixRank and LinearComp.
	var xor128 = requireXor128();

	// xorwow, George Marsaglia's 160-bit xor-shift combined plus weyl.
	// Period: 2^192-2^32
	// Reported to fail: CollisionOver, SimpPoker, and LinearComp.
	var xorwow = requireXorwow();

	// xorshift7, by François Panneton and Pierre L'ecuyer, takes
	// a different approach: it adds robustness by allowing more shifts
	// than Marsaglia's original three.  It is a 7-shift generator
	// with 256 bits, that passes BigCrush with no systmatic failures.
	// Period 2^256-1.
	// No systematic BigCrush failures reported.
	var xorshift7 = requireXorshift7();

	// xor4096, by Richard Brent, is a 4096-bit xor-shift with a
	// very long period that also adds a Weyl generator. It also passes
	// BigCrush with no systematic failures.  Its long period may
	// be useful if you have many generators and need to avoid
	// collisions.
	// Period: 2^4128-2^32.
	// No systematic BigCrush failures reported.
	var xor4096 = requireXor4096();

	// Tyche-i, by Samuel Neves and Filipe Araujo, is a bit-shifting random
	// number generator derived from ChaCha, a modern stream cipher.
	// https://eden.dei.uc.pt/~sneves/pubs/2011-snfa2.pdf
	// Period: ~2^127
	// No systematic BigCrush failures reported.
	var tychei = requireTychei();

	// The original ARC4-based prng included in this library.
	// Period: ~2^1600
	var sr = requireSeedrandom$1();

	sr.alea = alea;
	sr.xor128 = xor128;
	sr.xorwow = xorwow;
	sr.xorshift7 = xorshift7;
	sr.xor4096 = xor4096;
	sr.tychei = tychei;

	seedrandom = sr;
	return seedrandom;
}

var hasRequiredSeededRNG;

function requireSeededRNG () {
	if (hasRequiredSeededRNG) return seededRNG;
	hasRequiredSeededRNG = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(seededRNG, "__esModule", {
	  value: true
	});
	seededRNG.createRng = createRng;
	var _seedrandom = _interopRequireDefault(requireSeedrandom());
	const singletonRandom = /* #__PURE__ */(0, _seedrandom.default)(Date.now());
	function createRng(randomSeed) {
	  let random;

	  // create a new random generator with given seed
	  function setSeed(seed) {
	    random = seed === null ? singletonRandom : (0, _seedrandom.default)(String(seed));
	  }

	  // initialize a seeded pseudo random number generator with config's random seed
	  setSeed(randomSeed);

	  // wrapper function so the rng can be updated via generator
	  function rng() {
	    return random();
	  }
	  return rng;
	}
	return seededRNG;
}

var hasRequiredPickRandom;

function requirePickRandom () {
	if (hasRequiredPickRandom) return pickRandom;
	hasRequiredPickRandom = 1;

	Object.defineProperty(pickRandom, "__esModule", {
	  value: true
	});
	pickRandom.createPickRandom = void 0;
	var _array = requireArray();
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _seededRNG = requireSeededRNG();
	const name = 'pickRandom';
	const dependencies = ['typed', 'config', '?on'];
	pickRandom.createPickRandom = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    on
	  } = _ref;
	  // seeded pseudo random number generator
	  let rng = (0, _seededRNG.createRng)(config.randomSeed);
	  if (on) {
	    on('config', function (curr, prev) {
	      if (curr.randomSeed !== prev.randomSeed) {
	        rng = (0, _seededRNG.createRng)(curr.randomSeed);
	      }
	    });
	  }

	  /**
	   * Random pick one or more values from a one dimensional array.
	   * Array elements are picked using a random function with uniform or weighted distribution.
	   *
	   * Syntax:
	   *
	   *     math.pickRandom(array)
	   *     math.pickRandom(array, number)
	   *     math.pickRandom(array, weights)
	   *     math.pickRandom(array, number, weights)
	   *     math.pickRandom(array, weights, number)
	   *     math.pickRandom(array, { weights, number, elementWise })
	   *
	   * Examples:
	   *
	   *     math.pickRandom([3, 6, 12, 2])                  // returns one of the values in the array
	   *     math.pickRandom([3, 6, 12, 2], 2)               // returns an array of two of the values in the array
	   *     math.pickRandom([3, 6, 12, 2], { number: 2 })   // returns an array of two of the values in the array
	   *     math.pickRandom([3, 6, 12, 2], [1, 3, 2, 1])    // returns one of the values in the array with weighted distribution
	   *     math.pickRandom([3, 6, 12, 2], 2, [1, 3, 2, 1]) // returns an array of two of the values in the array with weighted distribution
	   *     math.pickRandom([3, 6, 12, 2], [1, 3, 2, 1], 2) // returns an array of two of the values in the array with weighted distribution
	   *
	   *     math.pickRandom([{x: 1.0, y: 2.0}, {x: 1.1, y: 2.0}], { elementWise: false })
	   *         // returns one of the items in the array
	   *
	   * See also:
	   *
	   *     random, randomInt
	   *
	   * @param {Array | Matrix} array     A one dimensional array
	   * @param {Int} number               An int or float
	   * @param {Array | Matrix} weights   An array of ints or floats
	   * @return {number | Array}          Returns a single random value from array when number is undefined.
	   *                                   Returns an array with the configured number of elements when number is defined.
	   */
	  return typed(name, {
	    'Array | Matrix': function (possibles) {
	      return _pickRandom(possibles, {});
	    },
	    'Array | Matrix, Object': function (possibles, options) {
	      return _pickRandom(possibles, options);
	    },
	    'Array | Matrix, number': function (possibles, number) {
	      return _pickRandom(possibles, {
	        number
	      });
	    },
	    'Array | Matrix, Array | Matrix': function (possibles, weights) {
	      return _pickRandom(possibles, {
	        weights
	      });
	    },
	    'Array | Matrix, Array | Matrix, number': function (possibles, weights, number) {
	      return _pickRandom(possibles, {
	        number,
	        weights
	      });
	    },
	    'Array | Matrix, number, Array | Matrix': function (possibles, number, weights) {
	      return _pickRandom(possibles, {
	        number,
	        weights
	      });
	    }
	  });

	  /**
	   * @param {Array | Matrix} possibles
	   * @param {{
	   *   number?: number,
	   *   weights?: Array | Matrix,
	   *   elementWise: boolean
	   * }} options
	   * @returns {number | Array}
	   * @private
	   */
	  function _pickRandom(possibles, _ref2) {
	    let {
	      number,
	      weights,
	      elementWise = true
	    } = _ref2;
	    const single = typeof number === 'undefined';
	    if (single) {
	      number = 1;
	    }
	    const createMatrix = (0, _is.isMatrix)(possibles) ? possibles.create : (0, _is.isMatrix)(weights) ? weights.create : null;
	    possibles = possibles.valueOf(); // get Array
	    if (weights) {
	      weights = weights.valueOf(); // get Array
	    }
	    if (elementWise === true) {
	      possibles = (0, _array.flatten)(possibles);
	      weights = (0, _array.flatten)(weights);
	    }
	    let totalWeights = 0;
	    if (typeof weights !== 'undefined') {
	      if (weights.length !== possibles.length) {
	        throw new Error('Weights must have the same length as possibles');
	      }
	      for (let i = 0, len = weights.length; i < len; i++) {
	        if (!(0, _is.isNumber)(weights[i]) || weights[i] < 0) {
	          throw new Error('Weights must be an array of positive numbers');
	        }
	        totalWeights += weights[i];
	      }
	    }
	    const length = possibles.length;
	    const result = [];
	    let pick;
	    while (result.length < number) {
	      if (typeof weights === 'undefined') {
	        pick = possibles[Math.floor(rng() * length)];
	      } else {
	        let randKey = rng() * totalWeights;
	        for (let i = 0, len = possibles.length; i < len; i++) {
	          randKey -= weights[i];
	          if (randKey < 0) {
	            pick = possibles[i];
	            break;
	          }
	        }
	      }
	      result.push(pick);
	    }
	    return single ? result[0] : createMatrix ? createMatrix(result) : result;
	  }
	});
	return pickRandom;
}

var random = {};

var randomMatrix = {};

var hasRequiredRandomMatrix;

function requireRandomMatrix () {
	if (hasRequiredRandomMatrix) return randomMatrix;
	hasRequiredRandomMatrix = 1;

	Object.defineProperty(randomMatrix, "__esModule", {
	  value: true
	});
	randomMatrix.randomMatrix = randomMatrix$1;
	/**
	 * This is a util function for generating a random matrix recursively.
	 * @param {number[]} size
	 * @param {function} random
	 * @returns {Array}
	 */
	function randomMatrix$1(size, random) {
	  const data = [];
	  size = size.slice(0);
	  if (size.length > 1) {
	    for (let i = 0, length = size.shift(); i < length; i++) {
	      data.push(randomMatrix$1(size, random));
	    }
	  } else {
	    for (let i = 0, length = size.shift(); i < length; i++) {
	      data.push(random());
	    }
	  }
	  return data;
	}
	return randomMatrix;
}

var hasRequiredRandom;

function requireRandom () {
	if (hasRequiredRandom) return random;
	hasRequiredRandom = 1;

	Object.defineProperty(random, "__esModule", {
	  value: true
	});
	random.createRandomNumber = random.createRandom = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _seededRNG = requireSeededRNG();
	var _randomMatrix2 = requireRandomMatrix();
	const name = 'random';
	const dependencies = ['typed', 'config', '?on'];
	random.createRandom = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    on
	  } = _ref;
	  // seeded pseudo random number generator
	  let rng = (0, _seededRNG.createRng)(config.randomSeed);
	  if (on) {
	    on('config', function (curr, prev) {
	      if (curr.randomSeed !== prev.randomSeed) {
	        rng = (0, _seededRNG.createRng)(curr.randomSeed);
	      }
	    });
	  }

	  /**
	   * Return a random number larger or equal to `min` and smaller than `max`
	   * using a uniform distribution.
	   *
	   * Syntax:
	   *
	   *     math.random()                // generate a random number between 0 and 1
	   *     math.random(max)             // generate a random number between 0 and max
	   *     math.random(min, max)        // generate a random number between min and max
	   *     math.random(size)            // generate a matrix with random numbers between 0 and 1
	   *     math.random(size, max)       // generate a matrix with random numbers between 0 and max
	   *     math.random(size, min, max)  // generate a matrix with random numbers between min and max
	   *
	   * Examples:
	   *
	   *     math.random()       // returns a random number between 0 and 1
	   *     math.random(100)    // returns a random number between 0 and 100
	   *     math.random(30, 40) // returns a random number between 30 and 40
	   *     math.random([2, 3]) // returns a 2x3 matrix with random numbers between 0 and 1
	   *
	   * See also:
	   *
	   *     randomInt, pickRandom
	   *
	   * @param {Array | Matrix} [size] If provided, an array or matrix with given
	   *                                size and filled with random values is returned
	   * @param {number} [min]  Minimum boundary for the random value, included
	   * @param {number} [max]  Maximum boundary for the random value, excluded
	   * @return {number | Array | Matrix} A random number
	   */
	  return typed(name, {
	    '': () => _random(0, 1),
	    number: max => _random(0, max),
	    'number, number': (min, max) => _random(min, max),
	    'Array | Matrix': size => _randomMatrix(size, 0, 1),
	    'Array | Matrix, number': (size, max) => _randomMatrix(size, 0, max),
	    'Array | Matrix, number, number': (size, min, max) => _randomMatrix(size, min, max)
	  });
	  function _randomMatrix(size, min, max) {
	    const res = (0, _randomMatrix2.randomMatrix)(size.valueOf(), () => _random(min, max));
	    return (0, _is.isMatrix)(size) ? size.create(res, 'number') : res;
	  }
	  function _random(min, max) {
	    return min + rng() * (max - min);
	  }
	});

	// number only implementation of random, no matrix support
	// TODO: there is quite some duplicate code in both createRandom and createRandomNumber, can we improve that?
	random.createRandomNumber = /* #__PURE__ */(0, _factory.factory)(name, ['typed', 'config', '?on'], _ref2 => {
	  let {
	    typed,
	    config,
	    on,
	    matrix
	  } = _ref2;
	  // seeded pseudo random number generator1
	  let rng = (0, _seededRNG.createRng)(config.randomSeed);
	  if (on) {
	    on('config', function (curr, prev) {
	      if (curr.randomSeed !== prev.randomSeed) {
	        rng = (0, _seededRNG.createRng)(curr.randomSeed);
	      }
	    });
	  }
	  return typed(name, {
	    '': () => _random(0, 1),
	    number: max => _random(0, max),
	    'number, number': (min, max) => _random(min, max)
	  });
	  function _random(min, max) {
	    return min + rng() * (max - min);
	  }
	});
	return random;
}

var randomInt = {};

var hasRequiredRandomInt;

function requireRandomInt () {
	if (hasRequiredRandomInt) return randomInt;
	hasRequiredRandomInt = 1;

	Object.defineProperty(randomInt, "__esModule", {
	  value: true
	});
	randomInt.createRandomInt = void 0;
	var _factory = requireFactory$b();
	var _randomMatrix = requireRandomMatrix();
	var _seededRNG = requireSeededRNG();
	var _is = requireIs();
	const name = 'randomInt';
	const dependencies = ['typed', 'config', 'log2', '?on'];
	randomInt.createRandomInt = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    log2,
	    on
	  } = _ref;
	  // seeded pseudo random number generator
	  let rng = (0, _seededRNG.createRng)(config.randomSeed);
	  if (on) {
	    on('config', function (curr, prev) {
	      if (curr.randomSeed !== prev.randomSeed) {
	        rng = (0, _seededRNG.createRng)(curr.randomSeed);
	      }
	    });
	  }

	  /**
	   * Return a random integer number larger or equal to `min` and smaller than `max`
	   * using a uniform distribution.
	   *
	   * Syntax:
	   *
	   *     math.randomInt()                // generate either 0 or 1, randomly
	   *     math.randomInt(max)             // generate a random integer between 0 and max
	   *     math.randomInt(min, max)        // generate a random integer between min and max
	   *     math.randomInt(size)            // generate a matrix with random integer between 0 and 1
	   *     math.randomInt(size, max)       // generate a matrix with random integer between 0 and max
	   *     math.randomInt(size, min, max)  // generate a matrix with random integer between min and max
	   *
	   * Examples:
	   *
	   *     math.randomInt(100)    // returns a random integer between 0 and 100
	   *     math.randomInt(30, 40) // returns a random integer between 30 and 40
	   *     math.randomInt([2, 3]) // returns a 2x3 matrix with random integers between 0 and 1
	   *
	   * See also:
	   *
	   *     random, pickRandom
	   *
	   * @param {Array | Matrix} [size] If provided, an array or matrix with given
	   *                                size and filled with random values is returned
	   * @param {number} [min]  Minimum boundary for the random value, included
	   * @param {number} [max]  Maximum boundary for the random value, excluded
	   * @return {number | Array | Matrix} A random integer value
	   */
	  return typed(name, {
	    '': () => _randomInt(0, 2),
	    number: max => _randomInt(0, max),
	    'number, number': (min, max) => _randomInt(min, max),
	    bigint: max => _randomBigint(0n, max),
	    'bigint, bigint': _randomBigint,
	    'Array | Matrix': size => _randomIntMatrix(size, 0, 1),
	    'Array | Matrix, number': (size, max) => _randomIntMatrix(size, 0, max),
	    'Array | Matrix, number, number': (size, min, max) => _randomIntMatrix(size, min, max)
	  });
	  function _randomIntMatrix(size, min, max) {
	    const res = (0, _randomMatrix.randomMatrix)(size.valueOf(), () => _randomInt(min, max));
	    return (0, _is.isMatrix)(size) ? size.create(res, 'number') : res;
	  }
	  function _randomInt(min, max) {
	    return Math.floor(min + rng() * (max - min));
	  }
	  function _randomBigint(min, max) {
	    const simpleCutoff = 2n ** 30n;
	    const width = max - min; // number of choices
	    if (width <= simpleCutoff) {
	      // do it with number type
	      return min + BigInt(_randomInt(0, Number(width)));
	    }
	    // Too big to choose accurately that way. Instead, choose the correct
	    // number of random bits to cover the width, and repeat until the
	    // resulting number falls within the width
	    const bits = log2(width);
	    let picked = width;
	    while (picked >= width) {
	      picked = 0n;
	      for (let i = 0; i < bits; ++i) {
	        picked = 2n * picked + (rng() < 0.5 ? 0n : 1n);
	      }
	    }
	    return min + picked;
	  }
	});
	return randomInt;
}

var stirlingS2 = {};

var hasRequiredStirlingS2;

function requireStirlingS2 () {
	if (hasRequiredStirlingS2) return stirlingS2;
	hasRequiredStirlingS2 = 1;

	Object.defineProperty(stirlingS2, "__esModule", {
	  value: true
	});
	stirlingS2.createStirlingS2 = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'stirlingS2';
	const dependencies = ['typed', 'addScalar', 'subtractScalar', 'multiplyScalar', 'divideScalar', 'pow', 'factorial', 'combinations', 'isNegative', 'isInteger', 'number', '?bignumber', 'larger'];
	stirlingS2.createStirlingS2 = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    addScalar,
	    subtractScalar,
	    multiplyScalar,
	    divideScalar,
	    pow,
	    factorial,
	    combinations,
	    isNegative,
	    isInteger,
	    number,
	    bignumber,
	    larger
	  } = _ref;
	  const smallCache = [];
	  const bigCache = [];
	  /**
	   * The Stirling numbers of the second kind, counts the number of ways to partition
	   * a set of n labelled objects into k nonempty unlabelled subsets.
	   * stirlingS2 only takes integer arguments.
	   * The following condition must be enforced: k <= n.
	   *
	   *  If n = k or k = 1 <= n, then s(n,k) = 1
	   *  If k = 0 < n, then s(n,k) = 0
	   *
	   * Note that if either n or k is supplied as a BigNumber, the result will be
	   * as well.
	   *
	   * Syntax:
	   *
	   *   math.stirlingS2(n, k)
	   *
	   * Examples:
	   *
	   *    math.stirlingS2(5, 3) //returns 25
	   *
	   * See also:
	   *
	   *    bellNumbers
	   *
	   * @param {Number | BigNumber} n    Total number of objects in the set
	   * @param {Number | BigNumber} k    Number of objects in the subset
	   * @return {Number | BigNumber}     S(n,k)
	   */
	  return typed(name, {
	    'number | BigNumber, number | BigNumber': function (n, k) {
	      if (!isInteger(n) || isNegative(n) || !isInteger(k) || isNegative(k)) {
	        throw new TypeError('Non-negative integer value expected in function stirlingS2');
	      } else if (larger(k, n)) {
	        throw new TypeError('k must be less than or equal to n in function stirlingS2');
	      }
	      const big = !((0, _is.isNumber)(n) && (0, _is.isNumber)(k));
	      const cache = big ? bigCache : smallCache;
	      const make = big ? bignumber : number;
	      const nn = number(n);
	      const nk = number(k);
	      /* See if we already have the value: */
	      if (cache[nn] && cache[nn].length > nk) {
	        return cache[nn][nk];
	      }
	      /* Fill the cache */
	      for (let m = 0; m <= nn; ++m) {
	        if (!cache[m]) {
	          cache[m] = [m === 0 ? make(1) : make(0)];
	        }
	        if (m === 0) continue;
	        const row = cache[m];
	        const prev = cache[m - 1];
	        for (let i = row.length; i <= m && i <= nk; ++i) {
	          if (i === m) {
	            row[i] = 1;
	          } else {
	            row[i] = addScalar(multiplyScalar(make(i), prev[i]), prev[i - 1]);
	          }
	        }
	      }
	      return cache[nn][nk];
	    }
	  });
	});
	return stirlingS2;
}

var bellNumbers = {};

var hasRequiredBellNumbers;

function requireBellNumbers () {
	if (hasRequiredBellNumbers) return bellNumbers;
	hasRequiredBellNumbers = 1;

	Object.defineProperty(bellNumbers, "__esModule", {
	  value: true
	});
	bellNumbers.createBellNumbers = void 0;
	var _factory = requireFactory$b();
	const name = 'bellNumbers';
	const dependencies = ['typed', 'addScalar', 'isNegative', 'isInteger', 'stirlingS2'];
	bellNumbers.createBellNumbers = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    addScalar,
	    isNegative,
	    isInteger,
	    stirlingS2
	  } = _ref;
	  /**
	   * The Bell Numbers count the number of partitions of a set. A partition is a pairwise disjoint subset of S whose union is S.
	   * bellNumbers only takes integer arguments.
	   * The following condition must be enforced: n >= 0
	   *
	   * Syntax:
	   *
	   *   math.bellNumbers(n)
	   *
	   * Examples:
	   *
	   *    math.bellNumbers(3) // returns 5
	   *    math.bellNumbers(8) // returns 4140
	   *
	   * See also:
	   *
	   *    stirlingS2
	   *
	   * @param {Number | BigNumber} n    Total number of objects in the set
	   * @return {Number | BigNumber}     B(n)
	   */
	  return typed(name, {
	    'number | BigNumber': function (n) {
	      if (!isInteger(n) || isNegative(n)) {
	        throw new TypeError('Non-negative integer value expected in function bellNumbers');
	      }

	      // Sum (k=0, n) S(n,k).
	      let result = 0;
	      for (let i = 0; i <= n; i++) {
	        result = addScalar(result, stirlingS2(n, i));
	      }
	      return result;
	    }
	  });
	});
	return bellNumbers;
}

var catalan = {};

var hasRequiredCatalan;

function requireCatalan () {
	if (hasRequiredCatalan) return catalan;
	hasRequiredCatalan = 1;

	Object.defineProperty(catalan, "__esModule", {
	  value: true
	});
	catalan.createCatalan = void 0;
	var _factory = requireFactory$b();
	const name = 'catalan';
	const dependencies = ['typed', 'addScalar', 'divideScalar', 'multiplyScalar', 'combinations', 'isNegative', 'isInteger'];
	catalan.createCatalan = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    addScalar,
	    divideScalar,
	    multiplyScalar,
	    combinations,
	    isNegative,
	    isInteger
	  } = _ref;
	  /**
	   * The Catalan Numbers enumerate combinatorial structures of many different types.
	   * catalan only takes integer arguments.
	   * The following condition must be enforced: n >= 0
	   *
	   * Syntax:
	   *
	   *   math.catalan(n)
	   *
	   * Examples:
	   *
	   *    math.catalan(3) // returns 5
	   *    math.catalan(8) // returns 1430
	   *
	   * See also:
	   *
	   *    bellNumbers
	   *
	   * @param {Number | BigNumber} n    nth Catalan number
	   * @return {Number | BigNumber}     Cn(n)
	   */
	  return typed(name, {
	    'number | BigNumber': function (n) {
	      if (!isInteger(n) || isNegative(n)) {
	        throw new TypeError('Non-negative integer value expected in function catalan');
	      }
	      return divideScalar(combinations(multiplyScalar(n, 2), n), addScalar(n, 1));
	    }
	  });
	});
	return catalan;
}

var composition = {};

var hasRequiredComposition;

function requireComposition () {
	if (hasRequiredComposition) return composition;
	hasRequiredComposition = 1;

	Object.defineProperty(composition, "__esModule", {
	  value: true
	});
	composition.createComposition = void 0;
	var _factory = requireFactory$b();
	const name = 'composition';
	const dependencies = ['typed', 'addScalar', 'combinations', 'isNegative', 'isPositive', 'isInteger', 'larger'];
	composition.createComposition = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    addScalar,
	    combinations,
	    isPositive,
	    isNegative,
	    isInteger,
	    larger
	  } = _ref;
	  /**
	   * The composition counts of n into k parts.
	   *
	   * composition only takes integer arguments.
	   * The following condition must be enforced: k <= n.
	   *
	   * Syntax:
	   *
	   *   math.composition(n, k)
	   *
	   * Examples:
	   *
	   *    math.composition(5, 3) // returns 6
	   *
	   * See also:
	   *
	   *    combinations
	   *
	   * @param {Number | BigNumber} n    Total number of objects in the set
	   * @param {Number | BigNumber} k    Number of objects in the subset
	   * @return {Number | BigNumber}     Returns the composition counts of n into k parts.
	   */
	  return typed(name, {
	    'number | BigNumber, number | BigNumber': function (n, k) {
	      if (!isInteger(n) || !isPositive(n) || !isInteger(k) || !isPositive(k)) {
	        throw new TypeError('Positive integer value expected in function composition');
	      } else if (larger(k, n)) {
	        throw new TypeError('k must be less than or equal to n in function composition');
	      }
	      return combinations(addScalar(n, -1), addScalar(k, -1));
	    }
	  });
	});
	return composition;
}

var leafCount = {};

var hasRequiredLeafCount;

function requireLeafCount () {
	if (hasRequiredLeafCount) return leafCount;
	hasRequiredLeafCount = 1;

	Object.defineProperty(leafCount, "__esModule", {
	  value: true
	});
	leafCount.createLeafCount = void 0;
	var _factory = requireFactory$b();
	const name = 'leafCount';
	const dependencies = ['parse', 'typed'];
	leafCount.createLeafCount = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    parse,
	    typed
	  } = _ref;
	  // This does the real work, but we don't have to recurse through
	  // a typed call if we separate it out
	  function countLeaves(node) {
	    let count = 0;
	    node.forEach(n => {
	      count += countLeaves(n);
	    });
	    return count || 1;
	  }

	  /**
	   * Gives the number of "leaf nodes" in the parse tree of the given expression
	   * A leaf node is one that has no subexpressions, essentially either a
	   * symbol or a constant. Note that `5!` has just one leaf, the '5'; the
	   * unary factorial operator does not add a leaf. On the other hand,
	   * function symbols do add leaves, so `sin(x)/cos(x)` has four leaves.
	   *
	   * The `simplify()` function should generally not increase the `leafCount()`
	   * of an expression, although currently there is no guarantee that it never
	   * does so. In many cases, `simplify()` reduces the leaf count.
	   *
	   * Syntax:
	   *
	   *     math.leafCount(expr)
	   *
	   * Examples:
	   *
	   *     math.leafCount('x') // 1
	   *     math.leafCount(math.parse('a*d-b*c')) // 4
	   *     math.leafCount('[a,b;c,d][0,1]') // 6
	   *
	   * See also:
	   *
	   *     simplify
	   *
	   * @param {Node|string} expr    The expression to count the leaves of
	   *
	   * @return {number}  The number of leaves of `expr`
	   *
	   */
	  return typed(name, {
	    Node: function (expr) {
	      return countLeaves(expr);
	    }
	  });
	});
	return leafCount;
}

var simplify = {};

var wildcards = {};

var hasRequiredWildcards;

function requireWildcards () {
	if (hasRequiredWildcards) return wildcards;
	hasRequiredWildcards = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		exports.isConstantExpression = isConstantExpression;
		Object.defineProperty(exports, "isConstantNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isConstantNode;
		  }
		});
		exports.isNumericNode = isNumericNode;
		Object.defineProperty(exports, "isVariableNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isSymbolNode;
		  }
		});
		var _is = requireIs();
		function isNumericNode(x) {
		  return (0, _is.isConstantNode)(x) || (0, _is.isOperatorNode)(x) && x.isUnary() && (0, _is.isConstantNode)(x.args[0]);
		}
		function isConstantExpression(x) {
		  if ((0, _is.isConstantNode)(x)) {
		    // Basic Constant types
		    return true;
		  }
		  if (((0, _is.isFunctionNode)(x) || (0, _is.isOperatorNode)(x)) && x.args.every(isConstantExpression)) {
		    // Can be constant depending on arguments
		    return true;
		  }
		  if ((0, _is.isParenthesisNode)(x) && isConstantExpression(x.content)) {
		    // Parenthesis are transparent
		    return true;
		  }
		  return false; // Probably missing some edge cases
		} 
	} (wildcards));
	return wildcards;
}

var util = {};

var hasRequiredUtil$1;

function requireUtil$1 () {
	if (hasRequiredUtil$1) return util;
	hasRequiredUtil$1 = 1;

	Object.defineProperty(util, "__esModule", {
	  value: true
	});
	util.createUtil = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _object = requireObject$3();
	const name = 'simplifyUtil';
	const dependencies = ['FunctionNode', 'OperatorNode', 'SymbolNode'];
	util.createUtil = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    FunctionNode,
	    OperatorNode,
	    SymbolNode
	  } = _ref;
	  // TODO commutative/associative properties rely on the arguments
	  // e.g. multiply is not commutative for matrices
	  // The properties should be calculated from an argument to simplify, or possibly something in math.config
	  // the other option is for typed() to specify a return type so that we can evaluate the type of arguments

	  /* So that properties of an operator fit on one line: */
	  const T = true;
	  const F = false;
	  const defaultName = 'defaultF';
	  const defaultContext = {
	    /*      */add: {
	      trivial: T,
	      total: T,
	      commutative: T,
	      associative: T
	    },
	    /**/unaryPlus: {
	      trivial: T,
	      total: T,
	      commutative: T,
	      associative: T
	    },
	    /* */subtract: {
	      trivial: F,
	      total: T,
	      commutative: F,
	      associative: F
	    },
	    /* */multiply: {
	      trivial: T,
	      total: T,
	      commutative: T,
	      associative: T
	    },
	    /*   */divide: {
	      trivial: F,
	      total: T,
	      commutative: F,
	      associative: F
	    },
	    /*    */paren: {
	      trivial: T,
	      total: T,
	      commutative: T,
	      associative: F
	    },
	    /* */defaultF: {
	      trivial: F,
	      total: T,
	      commutative: F,
	      associative: F
	    }
	  };
	  const realContext = {
	    divide: {
	      total: F
	    },
	    log: {
	      total: F
	    }
	  };
	  const positiveContext = {
	    subtract: {
	      total: F
	    },
	    abs: {
	      trivial: T
	    },
	    log: {
	      total: T
	    }
	  };
	  function hasProperty(nodeOrName, property) {
	    let context = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultContext;
	    let name = defaultName;
	    if (typeof nodeOrName === 'string') {
	      name = nodeOrName;
	    } else if ((0, _is.isOperatorNode)(nodeOrName)) {
	      name = nodeOrName.fn.toString();
	    } else if ((0, _is.isFunctionNode)(nodeOrName)) {
	      name = nodeOrName.name;
	    } else if ((0, _is.isParenthesisNode)(nodeOrName)) {
	      name = 'paren';
	    }
	    if ((0, _object.hasOwnProperty)(context, name)) {
	      const properties = context[name];
	      if ((0, _object.hasOwnProperty)(properties, property)) {
	        return properties[property];
	      }
	      if ((0, _object.hasOwnProperty)(defaultContext, name)) {
	        return defaultContext[name][property];
	      }
	    }
	    if ((0, _object.hasOwnProperty)(context, defaultName)) {
	      const properties = context[defaultName];
	      if ((0, _object.hasOwnProperty)(properties, property)) {
	        return properties[property];
	      }
	      return defaultContext[defaultName][property];
	    }
	    /* name not found in context and context has no global default */
	    /* So use default context. */
	    if ((0, _object.hasOwnProperty)(defaultContext, name)) {
	      const properties = defaultContext[name];
	      if ((0, _object.hasOwnProperty)(properties, property)) {
	        return properties[property];
	      }
	    }
	    return defaultContext[defaultName][property];
	  }
	  function isCommutative(node) {
	    let context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultContext;
	    return hasProperty(node, 'commutative', context);
	  }
	  function isAssociative(node) {
	    let context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultContext;
	    return hasProperty(node, 'associative', context);
	  }

	  /**
	   * Merge the given contexts, with primary overriding secondary
	   * wherever they might conflict
	   */
	  function mergeContext(primary, secondary) {
	    const merged = {
	      ...primary
	    };
	    for (const prop in secondary) {
	      if ((0, _object.hasOwnProperty)(primary, prop)) {
	        merged[prop] = {
	          ...secondary[prop],
	          ...primary[prop]
	        };
	      } else {
	        merged[prop] = secondary[prop];
	      }
	    }
	    return merged;
	  }

	  /**
	   * Flatten all associative operators in an expression tree.
	   * Assumes parentheses have already been removed.
	   */
	  function flatten(node, context) {
	    if (!node.args || node.args.length === 0) {
	      return node;
	    }
	    node.args = allChildren(node, context);
	    for (let i = 0; i < node.args.length; i++) {
	      flatten(node.args[i], context);
	    }
	  }

	  /**
	   * Get the children of a node as if it has been flattened.
	   * TODO implement for FunctionNodes
	   */
	  function allChildren(node, context) {
	    let op;
	    const children = [];
	    const findChildren = function (node) {
	      for (let i = 0; i < node.args.length; i++) {
	        const child = node.args[i];
	        if ((0, _is.isOperatorNode)(child) && op === child.op) {
	          findChildren(child);
	        } else {
	          children.push(child);
	        }
	      }
	    };
	    if (isAssociative(node, context)) {
	      op = node.op;
	      findChildren(node);
	      return children;
	    } else {
	      return node.args;
	    }
	  }

	  /**
	   *  Unflatten all flattened operators to a right-heavy binary tree.
	   */
	  function unflattenr(node, context) {
	    if (!node.args || node.args.length === 0) {
	      return;
	    }
	    const makeNode = createMakeNodeFunction(node);
	    const l = node.args.length;
	    for (let i = 0; i < l; i++) {
	      unflattenr(node.args[i], context);
	    }
	    if (l > 2 && isAssociative(node, context)) {
	      let curnode = node.args.pop();
	      while (node.args.length > 0) {
	        curnode = makeNode([node.args.pop(), curnode]);
	      }
	      node.args = curnode.args;
	    }
	  }

	  /**
	   *  Unflatten all flattened operators to a left-heavy binary tree.
	   */
	  function unflattenl(node, context) {
	    if (!node.args || node.args.length === 0) {
	      return;
	    }
	    const makeNode = createMakeNodeFunction(node);
	    const l = node.args.length;
	    for (let i = 0; i < l; i++) {
	      unflattenl(node.args[i], context);
	    }
	    if (l > 2 && isAssociative(node, context)) {
	      let curnode = node.args.shift();
	      while (node.args.length > 0) {
	        curnode = makeNode([curnode, node.args.shift()]);
	      }
	      node.args = curnode.args;
	    }
	  }
	  function createMakeNodeFunction(node) {
	    if ((0, _is.isOperatorNode)(node)) {
	      return function (args) {
	        try {
	          return new OperatorNode(node.op, node.fn, args, node.implicit);
	        } catch (err) {
	          console.error(err);
	          return [];
	        }
	      };
	    } else {
	      return function (args) {
	        return new FunctionNode(new SymbolNode(node.name), args);
	      };
	    }
	  }
	  return {
	    createMakeNodeFunction,
	    hasProperty,
	    isCommutative,
	    isAssociative,
	    mergeContext,
	    flatten,
	    allChildren,
	    unflattenr,
	    unflattenl,
	    defaultContext,
	    realContext,
	    positiveContext
	  };
	});
	return util;
}

var hasRequiredSimplify;

function requireSimplify () {
	if (hasRequiredSimplify) return simplify;
	hasRequiredSimplify = 1;

	Object.defineProperty(simplify, "__esModule", {
	  value: true
	});
	simplify.createSimplify = void 0;
	var _is = requireIs();
	var _wildcards = requireWildcards();
	var _factory = requireFactory$b();
	var _util = requireUtil$1();
	var _object = requireObject$3();
	var _map = requireMap$2();
	const name = 'simplify';
	const dependencies = ['typed', 'parse', 'equal', 'resolve', 'simplifyConstant', 'simplifyCore', 'AccessorNode', 'ArrayNode', 'ConstantNode', 'FunctionNode', 'IndexNode', 'ObjectNode', 'OperatorNode', 'ParenthesisNode', 'SymbolNode', 'replacer'];
	simplify.createSimplify = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    parse,
	    equal,
	    resolve,
	    simplifyConstant,
	    simplifyCore,
	    AccessorNode,
	    ArrayNode,
	    ConstantNode,
	    FunctionNode,
	    IndexNode,
	    ObjectNode,
	    OperatorNode,
	    ParenthesisNode,
	    SymbolNode,
	    replacer
	  } = _ref;
	  const {
	    hasProperty,
	    isCommutative,
	    isAssociative,
	    mergeContext,
	    flatten,
	    unflattenr,
	    unflattenl,
	    createMakeNodeFunction,
	    defaultContext,
	    realContext,
	    positiveContext
	  } = (0, _util.createUtil)({
	    FunctionNode,
	    OperatorNode,
	    SymbolNode
	  });

	  /**
	   * Simplify an expression tree.
	   *
	   * A list of rules are applied to an expression, repeating over the list until
	   * no further changes are made.
	   * It's possible to pass a custom set of rules to the function as second
	   * argument. A rule can be specified as an object, string, or function:
	   *
	   *     const rules = [
	   *       { l: 'n1*n3 + n2*n3', r: '(n1+n2)*n3' },
	   *       'n1*n3 + n2*n3 -> (n1+n2)*n3',
	   *       function (node) {
	   *         // ... return a new node or return the node unchanged
	   *         return node
	   *       }
	   *     ]
	   *
	   * String and object rules consist of a left and right pattern. The left is
	   * used to match against the expression and the right determines what matches
	   * are replaced with. The main difference between a pattern and a normal
	   * expression is that variables starting with the following characters are
	   * interpreted as wildcards:
	   *
	   * - 'n' - Matches any node [Node]
	   * - 'c' - Matches a constant literal (5 or 3.2) [ConstantNode]
	   * - 'cl' - Matches a constant literal; same as c [ConstantNode]
	   * - 'cd' - Matches a decimal literal (5 or -3.2) [ConstantNode or unaryMinus wrapping a ConstantNode]
	   * - 'ce' - Matches a constant expression (-5 or √3) [Expressions consisting of only ConstantNodes, functions, and operators]
	   * - 'v' - Matches a variable; anything not matched by c (-5 or x) [Node that is not a ConstantNode]
	   * - 'vl' - Matches a variable literal (x or y) [SymbolNode]
	   * - 'vd' - Matches a non-decimal expression; anything not matched by cd (x or √3) [Node that is not a ConstantNode or unaryMinus that is wrapping a ConstantNode]
	   * - 've' - Matches a variable expression; anything not matched by ce (x or 2x) [Expressions that contain a SymbolNode or other non-constant term]
	   *
	   * The default list of rules is exposed on the function as `simplify.rules`
	   * and can be used as a basis to built a set of custom rules. Note that since
	   * the `simplifyCore` function is in the default list of rules, by default
	   * simplify will convert any function calls in the expression that have
	   * operator equivalents to their operator forms.
	   *
	   * To specify a rule as a string, separate the left and right pattern by '->'
	   * When specifying a rule as an object, the following keys are meaningful:
	   * - l - the left pattern
	   * - r - the right pattern
	   * - s - in lieu of l and r, the string form that is broken at -> to give them
	   * - repeat - whether to repeat this rule until the expression stabilizes
	   * - assuming - gives a context object, as in the 'context' option to
	   *     simplify. Every property in the context object must match the current
	   *     context in order, or else the rule will not be applied.
	   * - imposeContext - gives a context object, as in the 'context' option to
	   *     simplify. Any settings specified will override the incoming context
	   *     for all matches of this rule.
	   *
	   * For more details on the theory, see:
	   *
	   * - [Strategies for simplifying math expressions (Stackoverflow)](https://stackoverflow.com/questions/7540227/strategies-for-simplifying-math-expressions)
	   * - [Symbolic computation - Simplification (Wikipedia)](https://en.wikipedia.org/wiki/Symbolic_computation#Simplification)
	   *
	   *  An optional `options` argument can be passed as last argument of `simplify`.
	   *  Currently available options (defaults in parentheses):
	   *  - `consoleDebug` (false): whether to write the expression being simplified
	   *    and any changes to it, along with the rule responsible, to console
	   *  - `context` (simplify.defaultContext): an object giving properties of
	   *    each operator, which determine what simplifications are allowed. The
	   *    currently meaningful properties are commutative, associative,
	   *    total (whether the operation is defined for all arguments), and
	   *    trivial (whether the operation applied to a single argument leaves
	   *    that argument unchanged). The default context is very permissive and
	   *    allows almost all simplifications. Only properties differing from
	   *    the default need to be specified; the default context is used as a
	   *    fallback. Additional contexts `simplify.realContext` and
	   *    `simplify.positiveContext` are supplied to cause simplify to perform
	   *    just simplifications guaranteed to preserve all values of the expression
	   *    assuming all variables and subexpressions are real numbers or
	   *    positive real numbers, respectively. (Note that these are in some cases
	   *    more restrictive than the default context; for example, the default
	   *    context will allow `x/x` to simplify to 1, whereas
	   *    `simplify.realContext` will not, as `0/0` is not equal to 1.)
	   *  - `exactFractions` (true): whether to try to convert all constants to
	   *    exact rational numbers.
	   *  - `fractionsLimit` (10000): when `exactFractions` is true, constants will
	   *    be expressed as fractions only when both numerator and denominator
	   *    are smaller than `fractionsLimit`.
	   *
	   * Syntax:
	   *
	   *     math.simplify(expr)
	   *     math.simplify(expr, rules)
	   *     math.simplify(expr, rules)
	   *     math.simplify(expr, rules, scope)
	   *     math.simplify(expr, rules, scope, options)
	   *     math.simplify(expr, scope)
	   *     math.simplify(expr, scope, options)
	   *
	   * Examples:
	   *
	   *     math.simplify('2 * 1 * x ^ (2 - 1)')      // Node "2 * x"
	   *     math.simplify('2 * 3 * x', {x: 4})        // Node "24"
	   *     const f = math.parse('2 * 1 * x ^ (2 - 1)')
	   *     math.simplify(f)                          // Node "2 * x"
	   *     math.simplify('0.4 * x', {}, {exactFractions: true})  // Node "x * 2 / 5"
	   *     math.simplify('0.4 * x', {}, {exactFractions: false}) // Node "0.4 * x"
	   *
	   * See also:
	   *
	   *     simplifyCore, derivative, evaluate, parse, rationalize, resolve
	   *
	   * @param {Node | string} expr
	   *            The expression to be simplified
	   * @param {SimplifyRule[]} [rules]
	   *            Optional list with custom rules
	   * @param {Object} [scope] Optional scope with variables
	   * @param {SimplifyOptions} [options] Optional configuration settings
	   * @return {Node} Returns the simplified form of `expr`
	   */
	  typed.addConversion({
	    from: 'Object',
	    to: 'Map',
	    convert: _map.createMap
	  });
	  const simplify = typed('simplify', {
	    Node: _simplify,
	    'Node, Map': (expr, scope) => _simplify(expr, false, scope),
	    'Node, Map, Object': (expr, scope, options) => _simplify(expr, false, scope, options),
	    'Node, Array': _simplify,
	    'Node, Array, Map': _simplify,
	    'Node, Array, Map, Object': _simplify
	  });
	  typed.removeConversion({
	    from: 'Object',
	    to: 'Map',
	    convert: _map.createMap
	  });
	  simplify.defaultContext = defaultContext;
	  simplify.realContext = realContext;
	  simplify.positiveContext = positiveContext;
	  function removeParens(node) {
	    return node.transform(function (node) {
	      return (0, _is.isParenthesisNode)(node) ? removeParens(node.content) : node;
	    });
	  }

	  // All constants that are allowed in rules
	  const SUPPORTED_CONSTANTS = {
	    true: true,
	    false: true,
	    e: true,
	    i: true,
	    Infinity: true,
	    LN2: true,
	    LN10: true,
	    LOG2E: true,
	    LOG10E: true,
	    NaN: true,
	    phi: true,
	    pi: true,
	    SQRT1_2: true,
	    SQRT2: true,
	    tau: true
	    // null: false,
	    // undefined: false,
	    // version: false,
	  };

	  // Array of strings, used to build the ruleSet.
	  // Each l (left side) and r (right side) are parsed by
	  // the expression parser into a node tree.
	  // Left hand sides are matched to subtrees within the
	  // expression to be parsed and replaced with the right
	  // hand side.
	  // TODO: Add support for constraints on constants (either in the form of a '=' expression or a callback [callback allows things like comparing symbols alphabetically])
	  // To evaluate lhs constants for rhs constants, use: { l: 'c1+c2', r: 'c3', evaluate: 'c3 = c1 + c2' }. Multiple assignments are separated by ';' in block format.
	  // It is possible to get into an infinite loop with conflicting rules
	  simplify.rules = [simplifyCore,
	  // { l: 'n+0', r: 'n' },     // simplifyCore
	  // { l: 'n^0', r: '1' },     // simplifyCore
	  // { l: '0*n', r: '0' },     // simplifyCore
	  // { l: 'n/n', r: '1'},      // simplifyCore
	  // { l: 'n^1', r: 'n' },     // simplifyCore
	  // { l: '+n1', r:'n1' },     // simplifyCore
	  // { l: 'n--n1', r:'n+n1' }, // simplifyCore
	  {
	    l: 'log(e)',
	    r: '1'
	  },
	  // temporary rules
	  // Note initially we tend constants to the right because like-term
	  // collection prefers the left, and we would rather collect nonconstants
	  {
	    s: 'n-n1 -> n+-n1',
	    // temporarily replace 'subtract' so we can further flatten the 'add' operator
	    assuming: {
	      subtract: {
	        total: true
	      }
	    }
	  }, {
	    s: 'n-n -> 0',
	    // partial alternative when we can't always subtract
	    assuming: {
	      subtract: {
	        total: false
	      }
	    }
	  }, {
	    s: '-(cl*v) -> v * (-cl)',
	    // make non-constant terms positive
	    assuming: {
	      multiply: {
	        commutative: true
	      },
	      subtract: {
	        total: true
	      }
	    }
	  }, {
	    s: '-(cl*v) -> (-cl) * v',
	    // non-commutative version, part 1
	    assuming: {
	      multiply: {
	        commutative: false
	      },
	      subtract: {
	        total: true
	      }
	    }
	  }, {
	    s: '-(v*cl) -> v * (-cl)',
	    // non-commutative version, part 2
	    assuming: {
	      multiply: {
	        commutative: false
	      },
	      subtract: {
	        total: true
	      }
	    }
	  }, {
	    l: '-(n1/n2)',
	    r: '-n1/n2'
	  }, {
	    l: '-v',
	    r: 'v * (-1)'
	  },
	  // finish making non-constant terms positive
	  {
	    l: '(n1 + n2)*(-1)',
	    r: 'n1*(-1) + n2*(-1)',
	    repeat: true
	  },
	  // expand negations to achieve as much sign cancellation as possible
	  {
	    l: 'n/n1^n2',
	    r: 'n*n1^-n2'
	  },
	  // temporarily replace 'divide' so we can further flatten the 'multiply' operator
	  {
	    l: 'n/n1',
	    r: 'n*n1^-1'
	  }, {
	    s: '(n1*n2)^n3 -> n1^n3 * n2^n3',
	    assuming: {
	      multiply: {
	        commutative: true
	      }
	    }
	  }, {
	    s: '(n1*n2)^(-1) -> n2^(-1) * n1^(-1)',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  },
	  // expand nested exponentiation
	  {
	    s: '(n ^ n1) ^ n2 -> n ^ (n1 * n2)',
	    assuming: {
	      divide: {
	        total: true
	      }
	    } // 1/(1/n) = n needs 1/n to exist
	  },
	  // collect like factors; into a sum, only do this for nonconstants
	  {
	    l: ' vd   * ( vd   * n1 + n2)',
	    r: 'vd^2       * n1 +  vd   * n2'
	  }, {
	    s: ' vd   * (vd^n4 * n1 + n2)   ->  vd^(1+n4)  * n1 +  vd   * n2',
	    assuming: {
	      divide: {
	        total: true
	      }
	    } // v*1/v = v^(1+-1) needs 1/v
	  }, {
	    s: 'vd^n3 * ( vd   * n1 + n2)   ->  vd^(n3+1)  * n1 + vd^n3 * n2',
	    assuming: {
	      divide: {
	        total: true
	      }
	    }
	  }, {
	    s: 'vd^n3 * (vd^n4 * n1 + n2)   ->  vd^(n3+n4) * n1 + vd^n3 * n2',
	    assuming: {
	      divide: {
	        total: true
	      }
	    }
	  }, {
	    l: 'n*n',
	    r: 'n^2'
	  }, {
	    s: 'n * n^n1 -> n^(n1+1)',
	    assuming: {
	      divide: {
	        total: true
	      }
	    } // n*1/n = n^(-1+1) needs 1/n
	  }, {
	    s: 'n^n1 * n^n2 -> n^(n1+n2)',
	    assuming: {
	      divide: {
	        total: true
	      }
	    } // ditto for n^2*1/n^2
	  },
	  // Unfortunately, to deal with more complicated cancellations, it
	  // becomes necessary to simplify constants twice per pass. It's not
	  // terribly expensive compared to matching rules, so this should not
	  // pose a performance problem.
	  simplifyConstant,
	  // First: before collecting like terms

	  // collect like terms
	  {
	    s: 'n+n -> 2*n',
	    assuming: {
	      add: {
	        total: true
	      }
	    } // 2 = 1 + 1 needs to exist
	  }, {
	    l: 'n+-n',
	    r: '0'
	  }, {
	    l: 'vd*n + vd',
	    r: 'vd*(n+1)'
	  },
	  // NOTE: leftmost position is special:
	  {
	    l: 'n3*n1 + n3*n2',
	    r: 'n3*(n1+n2)'
	  },
	  // All sub-monomials tried there.
	  {
	    l: 'n3^(-n4)*n1 +   n3  * n2',
	    r: 'n3^(-n4)*(n1 + n3^(n4+1) *n2)'
	  }, {
	    l: 'n3^(-n4)*n1 + n3^n5 * n2',
	    r: 'n3^(-n4)*(n1 + n3^(n4+n5)*n2)'
	  },
	  // noncommutative additional cases (term collection & factoring)
	  {
	    s: 'n*vd + vd -> (n+1)*vd',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'vd + n*vd -> (1+n)*vd',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'n1*n3 + n2*n3 -> (n1+n2)*n3',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'n^n1 * n -> n^(n1+1)',
	    assuming: {
	      divide: {
	        total: true
	      },
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'n1*n3^(-n4) + n2 * n3    -> (n1 + n2*n3^(n4 +  1))*n3^(-n4)',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'n1*n3^(-n4) + n2 * n3^n5 -> (n1 + n2*n3^(n4 + n5))*n3^(-n4)',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    l: 'n*cd + cd',
	    r: '(n+1)*cd'
	  }, {
	    s: 'cd*n + cd -> cd*(n+1)',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'cd + cd*n -> cd*(1+n)',
	    assuming: {
	      multiply: {
	        commutative: false
	      }
	    }
	  }, simplifyConstant,
	  // Second: before returning expressions to "standard form"

	  // make factors positive (and undo 'make non-constant terms positive')
	  {
	    s: '(-n)*n1 -> -(n*n1)',
	    assuming: {
	      subtract: {
	        total: true
	      }
	    }
	  }, {
	    s: 'n1*(-n) -> -(n1*n)',
	    // in case * non-commutative
	    assuming: {
	      subtract: {
	        total: true
	      },
	      multiply: {
	        commutative: false
	      }
	    }
	  },
	  // final ordering of constants
	  {
	    s: 'ce+ve -> ve+ce',
	    assuming: {
	      add: {
	        commutative: true
	      }
	    },
	    imposeContext: {
	      add: {
	        commutative: false
	      }
	    }
	  }, {
	    s: 'vd*cd -> cd*vd',
	    assuming: {
	      multiply: {
	        commutative: true
	      }
	    },
	    imposeContext: {
	      multiply: {
	        commutative: false
	      }
	    }
	  },
	  // undo temporary rules
	  // { l: '(-1) * n', r: '-n' }, // #811 added test which proved this is redundant
	  {
	    l: 'n+-n1',
	    r: 'n-n1'
	  },
	  // undo replace 'subtract'
	  {
	    l: 'n+-(n1)',
	    r: 'n-(n1)'
	  }, {
	    s: 'n*(n1^-1) -> n/n1',
	    // undo replace 'divide'; for * commutative
	    assuming: {
	      multiply: {
	        commutative: true
	      }
	    } // o.w. / not conventional
	  }, {
	    s: 'n*n1^-n2 -> n/n1^n2',
	    assuming: {
	      multiply: {
	        commutative: true
	      }
	    } // o.w. / not conventional
	  }, {
	    s: 'n^-1 -> 1/n',
	    assuming: {
	      multiply: {
	        commutative: true
	      }
	    } // o.w. / not conventional
	  }, {
	    l: 'n^1',
	    r: 'n'
	  },
	  // can be produced by power cancellation
	  {
	    s: 'n*(n1/n2) -> (n*n1)/n2',
	    // '*' before '/'
	    assuming: {
	      multiply: {
	        associative: true
	      }
	    }
	  }, {
	    s: 'n-(n1+n2) -> n-n1-n2',
	    // '-' before '+'
	    assuming: {
	      addition: {
	        associative: true,
	        commutative: true
	      }
	    }
	  },
	  // { l: '(n1/n2)/n3', r: 'n1/(n2*n3)' },
	  // { l: '(n*n1)/(n*n2)', r: 'n1/n2' },

	  // simplifyConstant can leave an extra factor of 1, which can always
	  // be eliminated, since the identity always commutes
	  {
	    l: '1*n',
	    r: 'n',
	    imposeContext: {
	      multiply: {
	        commutative: true
	      }
	    }
	  }, {
	    s: 'n1/(n2/n3) -> (n1*n3)/n2',
	    assuming: {
	      multiply: {
	        associative: true
	      }
	    }
	  }, {
	    l: 'n1/(-n2)',
	    r: '-n1/n2'
	  }];

	  /**
	   * Takes any rule object as allowed by the specification in simplify
	   * and puts it in a standard form used by applyRule
	   */
	  function _canonicalizeRule(ruleObject, context) {
	    const newRule = {};
	    if (ruleObject.s) {
	      const lr = ruleObject.s.split('->');
	      if (lr.length === 2) {
	        newRule.l = lr[0];
	        newRule.r = lr[1];
	      } else {
	        throw SyntaxError('Could not parse rule: ' + ruleObject.s);
	      }
	    } else {
	      newRule.l = ruleObject.l;
	      newRule.r = ruleObject.r;
	    }
	    newRule.l = removeParens(parse(newRule.l));
	    newRule.r = removeParens(parse(newRule.r));
	    for (const prop of ['imposeContext', 'repeat', 'assuming']) {
	      if (prop in ruleObject) {
	        newRule[prop] = ruleObject[prop];
	      }
	    }
	    if (ruleObject.evaluate) {
	      newRule.evaluate = parse(ruleObject.evaluate);
	    }
	    if (isAssociative(newRule.l, context)) {
	      const nonCommutative = !isCommutative(newRule.l, context);
	      let leftExpandsym;
	      // Gen. the LHS placeholder used in this NC-context specific expansion rules
	      if (nonCommutative) leftExpandsym = _getExpandPlaceholderSymbol();
	      const makeNode = createMakeNodeFunction(newRule.l);
	      const expandsym = _getExpandPlaceholderSymbol();
	      newRule.expanded = {};
	      newRule.expanded.l = makeNode([newRule.l, expandsym]);
	      // Push the expandsym into the deepest possible branch.
	      // This helps to match the newRule against nodes returned from getSplits() later on.
	      flatten(newRule.expanded.l, context);
	      unflattenr(newRule.expanded.l, context);
	      newRule.expanded.r = makeNode([newRule.r, expandsym]);

	      // In and for a non-commutative context, attempting with yet additional expansion rules makes
	      // way for more matches cases of multi-arg expressions; such that associative rules (such as
	      // 'n*n -> n^2') can be applied to exprs. such as 'a * b * b' and 'a * b * b * a'.
	      if (nonCommutative) {
	        // 'Non-commutative' 1: LHS (placeholder) only
	        newRule.expandedNC1 = {};
	        newRule.expandedNC1.l = makeNode([leftExpandsym, newRule.l]);
	        newRule.expandedNC1.r = makeNode([leftExpandsym, newRule.r]);
	        // 'Non-commutative' 2: farmost LHS and RHS placeholders
	        newRule.expandedNC2 = {};
	        newRule.expandedNC2.l = makeNode([leftExpandsym, newRule.expanded.l]);
	        newRule.expandedNC2.r = makeNode([leftExpandsym, newRule.expanded.r]);
	      }
	    }
	    return newRule;
	  }

	  /**
	   * Parse the string array of rules into nodes
	   *
	   * Example syntax for rules:
	   *
	   * Position constants to the left in a product:
	   * { l: 'n1 * c1', r: 'c1 * n1' }
	   * n1 is any Node, and c1 is a ConstantNode.
	   *
	   * Apply difference of squares formula:
	   * { l: '(n1 - n2) * (n1 + n2)', r: 'n1^2 - n2^2' }
	   * n1, n2 mean any Node.
	   *
	   * Short hand notation:
	   * 'n1 * c1 -> c1 * n1'
	   */
	  function _buildRules(rules, context) {
	    // Array of rules to be used to simplify expressions
	    const ruleSet = [];
	    for (let i = 0; i < rules.length; i++) {
	      let rule = rules[i];
	      let newRule;
	      const ruleType = typeof rule;
	      switch (ruleType) {
	        case 'string':
	          rule = {
	            s: rule
	          };
	        /* falls through */
	        case 'object':
	          newRule = _canonicalizeRule(rule, context);
	          break;
	        case 'function':
	          newRule = rule;
	          break;
	        default:
	          throw TypeError('Unsupported type of rule: ' + ruleType);
	      }
	      // console.log('Adding rule: ' + rules[i])
	      // console.log(newRule)
	      ruleSet.push(newRule);
	    }
	    return ruleSet;
	  }
	  let _lastsym = 0;
	  function _getExpandPlaceholderSymbol() {
	    return new SymbolNode('_p' + _lastsym++);
	  }
	  function _simplify(expr, rules) {
	    let scope = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : (0, _map.createEmptyMap)();
	    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
	    const debug = options.consoleDebug;
	    rules = _buildRules(rules || simplify.rules, options.context);
	    let res = resolve(expr, scope);
	    res = removeParens(res);
	    const visited = {};
	    let str = res.toString({
	      parenthesis: 'all'
	    });
	    while (!visited[str]) {
	      visited[str] = true;
	      _lastsym = 0; // counter for placeholder symbols
	      let laststr = str;
	      if (debug) console.log('Working on: ', str);
	      for (let i = 0; i < rules.length; i++) {
	        let rulestr = '';
	        if (typeof rules[i] === 'function') {
	          res = rules[i](res, options);
	          if (debug) rulestr = rules[i].name;
	        } else {
	          flatten(res, options.context);
	          res = applyRule(res, rules[i], options.context);
	          if (debug) {
	            rulestr = `${rules[i].l.toString()} -> ${rules[i].r.toString()}`;
	          }
	        }
	        if (debug) {
	          const newstr = res.toString({
	            parenthesis: 'all'
	          });
	          if (newstr !== laststr) {
	            console.log('Applying', rulestr, 'produced', newstr);
	            laststr = newstr;
	          }
	        }
	        /* Use left-heavy binary tree internally,
	         * since custom rule functions may expect it
	         */
	        unflattenl(res, options.context);
	      }
	      str = res.toString({
	        parenthesis: 'all'
	      });
	    }
	    return res;
	  }
	  function mapRule(nodes, rule, context) {
	    let resNodes = nodes;
	    if (nodes) {
	      for (let i = 0; i < nodes.length; ++i) {
	        const newNode = applyRule(nodes[i], rule, context);
	        if (newNode !== nodes[i]) {
	          if (resNodes === nodes) {
	            resNodes = nodes.slice();
	          }
	          resNodes[i] = newNode;
	        }
	      }
	    }
	    return resNodes;
	  }

	  /**
	   * Returns a simplfied form of node, or the original node if no simplification was possible.
	   *
	   * @param  {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} node
	   * @param  {Object | Function} rule
	   * @param  {Object} context -- information about assumed properties of operators
	   * @return {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} The simplified form of `expr`, or the original node if no simplification was possible.
	   */
	  function applyRule(node, rule, context) {
	    //    console.log('Entering applyRule("', rule.l.toString({parenthesis:'all'}), '->', rule.r.toString({parenthesis:'all'}), '",', node.toString({parenthesis:'all'}),')')

	    // check that the assumptions for this rule are satisfied by the current
	    // context:
	    if (rule.assuming) {
	      for (const symbol in rule.assuming) {
	        for (const property in rule.assuming[symbol]) {
	          if (hasProperty(symbol, property, context) !== rule.assuming[symbol][property]) {
	            return node;
	          }
	        }
	      }
	    }
	    const mergedContext = mergeContext(rule.imposeContext, context);

	    // Do not clone node unless we find a match
	    let res = node;

	    // First replace our child nodes with their simplified versions
	    // If a child could not be simplified, applying the rule to it
	    // will have no effect since the node is returned unchanged
	    if (res instanceof OperatorNode || res instanceof FunctionNode) {
	      const newArgs = mapRule(res.args, rule, context);
	      if (newArgs !== res.args) {
	        res = res.clone();
	        res.args = newArgs;
	      }
	    } else if (res instanceof ParenthesisNode) {
	      if (res.content) {
	        const newContent = applyRule(res.content, rule, context);
	        if (newContent !== res.content) {
	          res = new ParenthesisNode(newContent);
	        }
	      }
	    } else if (res instanceof ArrayNode) {
	      const newItems = mapRule(res.items, rule, context);
	      if (newItems !== res.items) {
	        res = new ArrayNode(newItems);
	      }
	    } else if (res instanceof AccessorNode) {
	      let newObj = res.object;
	      if (res.object) {
	        newObj = applyRule(res.object, rule, context);
	      }
	      let newIndex = res.index;
	      if (res.index) {
	        newIndex = applyRule(res.index, rule, context);
	      }
	      if (newObj !== res.object || newIndex !== res.index) {
	        res = new AccessorNode(newObj, newIndex);
	      }
	    } else if (res instanceof IndexNode) {
	      const newDims = mapRule(res.dimensions, rule, context);
	      if (newDims !== res.dimensions) {
	        res = new IndexNode(newDims);
	      }
	    } else if (res instanceof ObjectNode) {
	      let changed = false;
	      const newProps = {};
	      for (const prop in res.properties) {
	        newProps[prop] = applyRule(res.properties[prop], rule, context);
	        if (newProps[prop] !== res.properties[prop]) {
	          changed = true;
	        }
	      }
	      if (changed) {
	        res = new ObjectNode(newProps);
	      }
	    }

	    // Try to match a rule against this node
	    let repl = rule.r;
	    let matches = _ruleMatch(rule.l, res, mergedContext)[0];

	    // If the rule is associative operator, we can try matching it while allowing additional terms.
	    // This allows us to match rules like 'n+n' to the expression '(1+x)+x' or even 'x+1+x' if the operator is commutative.
	    if (!matches && rule.expanded) {
	      repl = rule.expanded.r;
	      matches = _ruleMatch(rule.expanded.l, res, mergedContext)[0];
	    }
	    // Additional, non-commutative context expansion-rules
	    if (!matches && rule.expandedNC1) {
	      repl = rule.expandedNC1.r;
	      matches = _ruleMatch(rule.expandedNC1.l, res, mergedContext)[0];
	      if (!matches) {
	        // Existence of NC1 implies NC2
	        repl = rule.expandedNC2.r;
	        matches = _ruleMatch(rule.expandedNC2.l, res, mergedContext)[0];
	      }
	    }
	    if (matches) {
	      // const before = res.toString({parenthesis: 'all'})

	      // Create a new node by cloning the rhs of the matched rule
	      // we keep any implicit multiplication state if relevant
	      const implicit = res.implicit;
	      res = repl.clone();
	      if (implicit && 'implicit' in repl) {
	        res.implicit = true;
	      }

	      // Replace placeholders with their respective nodes without traversing deeper into the replaced nodes
	      res = res.transform(function (node) {
	        if (node.isSymbolNode && (0, _object.hasOwnProperty)(matches.placeholders, node.name)) {
	          return matches.placeholders[node.name].clone();
	        } else {
	          return node;
	        }
	      });

	      // const after = res.toString({parenthesis: 'all'})
	      // console.log('Simplified ' + before + ' to ' + after)
	    }
	    if (rule.repeat && res !== node) {
	      res = applyRule(res, rule, context);
	    }
	    return res;
	  }

	  /**
	   * Get (binary) combinations of a flattened binary node
	   * e.g. +(node1, node2, node3) -> [
	   *        +(node1,  +(node2, node3)),
	   *        +(node2,  +(node1, node3)),
	   *        +(node3,  +(node1, node2))]
	   *
	   */
	  function getSplits(node, context) {
	    const res = [];
	    let right, rightArgs;
	    const makeNode = createMakeNodeFunction(node);
	    if (isCommutative(node, context)) {
	      for (let i = 0; i < node.args.length; i++) {
	        rightArgs = node.args.slice(0);
	        rightArgs.splice(i, 1);
	        right = rightArgs.length === 1 ? rightArgs[0] : makeNode(rightArgs);
	        res.push(makeNode([node.args[i], right]));
	      }
	    } else {
	      // Keep order, but try all parenthesizations
	      for (let i = 1; i < node.args.length; i++) {
	        let left = node.args[0];
	        if (i > 1) {
	          left = makeNode(node.args.slice(0, i));
	        }
	        rightArgs = node.args.slice(i);
	        right = rightArgs.length === 1 ? rightArgs[0] : makeNode(rightArgs);
	        res.push(makeNode([left, right]));
	      }
	    }
	    return res;
	  }

	  /**
	   * Returns the set union of two match-placeholders or null if there is a conflict.
	   */
	  function mergeMatch(match1, match2) {
	    const res = {
	      placeholders: {}
	    };

	    // Some matches may not have placeholders; this is OK
	    if (!match1.placeholders && !match2.placeholders) {
	      return res;
	    } else if (!match1.placeholders) {
	      return match2;
	    } else if (!match2.placeholders) {
	      return match1;
	    }

	    // Placeholders with the same key must match exactly
	    for (const key in match1.placeholders) {
	      if ((0, _object.hasOwnProperty)(match1.placeholders, key)) {
	        res.placeholders[key] = match1.placeholders[key];
	        if ((0, _object.hasOwnProperty)(match2.placeholders, key)) {
	          if (!_exactMatch(match1.placeholders[key], match2.placeholders[key])) {
	            return null;
	          }
	        }
	      }
	    }
	    for (const key in match2.placeholders) {
	      if ((0, _object.hasOwnProperty)(match2.placeholders, key)) {
	        res.placeholders[key] = match2.placeholders[key];
	      }
	    }
	    return res;
	  }

	  /**
	   * Combine two lists of matches by applying mergeMatch to the cartesian product of two lists of matches.
	   * Each list represents matches found in one child of a node.
	   */
	  function combineChildMatches(list1, list2) {
	    const res = [];
	    if (list1.length === 0 || list2.length === 0) {
	      return res;
	    }
	    let merged;
	    for (let i1 = 0; i1 < list1.length; i1++) {
	      for (let i2 = 0; i2 < list2.length; i2++) {
	        merged = mergeMatch(list1[i1], list2[i2]);
	        if (merged) {
	          res.push(merged);
	        }
	      }
	    }
	    return res;
	  }

	  /**
	   * Combine multiple lists of matches by applying mergeMatch to the cartesian product of two lists of matches.
	   * Each list represents matches found in one child of a node.
	   * Returns a list of unique matches.
	   */
	  function mergeChildMatches(childMatches) {
	    if (childMatches.length === 0) {
	      return childMatches;
	    }
	    const sets = childMatches.reduce(combineChildMatches);
	    const uniqueSets = [];
	    const unique = {};
	    for (let i = 0; i < sets.length; i++) {
	      const s = JSON.stringify(sets[i], replacer);
	      if (!unique[s]) {
	        unique[s] = true;
	        uniqueSets.push(sets[i]);
	      }
	    }
	    return uniqueSets;
	  }

	  /**
	   * Determines whether node matches rule.
	   *
	   * @param {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} rule
	   * @param {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} node
	   * @param {Object} context -- provides assumed properties of operators
	   * @param {Boolean} isSplit -- whether we are in process of splitting an
	   *                    n-ary operator node into possible binary combinations.
	   *                    Defaults to false.
	   * @return {Object} Information about the match, if it exists.
	   */
	  function _ruleMatch(rule, node, context, isSplit) {
	    //    console.log('Entering _ruleMatch(' + JSON.stringify(rule) + ', ' + JSON.stringify(node) + ')')
	    //    console.log('rule = ' + rule)
	    //    console.log('node = ' + node)

	    //    console.log('Entering _ruleMatch(', rule.toString({parenthesis:'all'}), ', ', node.toString({parenthesis:'all'}), ', ', context, ')')
	    let res = [{
	      placeholders: {}
	    }];
	    if (rule instanceof OperatorNode && node instanceof OperatorNode || rule instanceof FunctionNode && node instanceof FunctionNode) {
	      // If the rule is an OperatorNode or a FunctionNode, then node must match exactly
	      if (rule instanceof OperatorNode) {
	        if (rule.op !== node.op || rule.fn !== node.fn) {
	          return [];
	        }
	      } else if (rule instanceof FunctionNode) {
	        if (rule.name !== node.name) {
	          return [];
	        }
	      }

	      // rule and node match. Search the children of rule and node.
	      if (node.args.length === 1 && rule.args.length === 1 || !isAssociative(node, context) && node.args.length === rule.args.length || isSplit) {
	        // Expect non-associative operators to match exactly,
	        // except in any order if operator is commutative
	        let childMatches = [];
	        for (let i = 0; i < rule.args.length; i++) {
	          const childMatch = _ruleMatch(rule.args[i], node.args[i], context);
	          if (childMatch.length === 0) {
	            // Child did not match, so stop searching immediately
	            break;
	          }
	          // The child matched, so add the information returned from the child to our result
	          childMatches.push(childMatch);
	        }
	        if (childMatches.length !== rule.args.length) {
	          if (!isCommutative(node, context) ||
	          // exact match in order needed
	          rule.args.length === 1) {
	            // nothing to commute
	            return [];
	          }
	          if (rule.args.length > 2) {
	            /* Need to generate all permutations and try them.
	             * It's a bit complicated, and unlikely to come up since there
	             * are very few ternary or higher operators. So punt for now.
	             */
	            throw new Error('permuting >2 commutative non-associative rule arguments not yet implemented');
	          }
	          /* Exactly two arguments, try them reversed */
	          const leftMatch = _ruleMatch(rule.args[0], node.args[1], context);
	          if (leftMatch.length === 0) {
	            return [];
	          }
	          const rightMatch = _ruleMatch(rule.args[1], node.args[0], context);
	          if (rightMatch.length === 0) {
	            return [];
	          }
	          childMatches = [leftMatch, rightMatch];
	        }
	        res = mergeChildMatches(childMatches);
	      } else if (node.args.length >= 2 && rule.args.length === 2) {
	        // node is flattened, rule is not
	        // Associative operators/functions can be split in different ways so we check if the rule
	        // matches for each of them and return their union.
	        const splits = getSplits(node, context);
	        let splitMatches = [];
	        for (let i = 0; i < splits.length; i++) {
	          const matchSet = _ruleMatch(rule, splits[i], context, true); // recursing at the same tree depth here
	          splitMatches = splitMatches.concat(matchSet);
	        }
	        return splitMatches;
	      } else if (rule.args.length > 2) {
	        throw Error('Unexpected non-binary associative function: ' + rule.toString());
	      } else {
	        // Incorrect number of arguments in rule and node, so no match
	        return [];
	      }
	    } else if (rule instanceof SymbolNode) {
	      // If the rule is a SymbolNode, then it carries a special meaning
	      // according to the first one or two characters of the symbol node name.
	      // These meanings are expalined in the documentation for simplify()
	      if (rule.name.length === 0) {
	        throw new Error('Symbol in rule has 0 length...!?');
	      }
	      if (SUPPORTED_CONSTANTS[rule.name]) {
	        // built-in constant must match exactly
	        if (rule.name !== node.name) {
	          return [];
	        }
	      } else {
	        // wildcards are composed of up to two alphabetic or underscore characters
	        switch (rule.name[1] >= 'a' && rule.name[1] <= 'z' ? rule.name.substring(0, 2) : rule.name[0]) {
	          case 'n':
	          case '_p':
	            // rule matches _anything_, so assign this node to the rule.name placeholder
	            // Assign node to the rule.name placeholder.
	            // Our parent will check for matches among placeholders.
	            res[0].placeholders[rule.name] = node;
	            break;
	          case 'c':
	          case 'cl':
	            // rule matches a ConstantNode
	            if ((0, _wildcards.isConstantNode)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          case 'v':
	            // rule matches anything other than a ConstantNode
	            if (!(0, _wildcards.isConstantNode)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          case 'vl':
	            // rule matches VariableNode
	            if ((0, _wildcards.isVariableNode)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          case 'cd':
	            // rule matches a ConstantNode or unaryMinus-wrapped ConstantNode
	            if ((0, _wildcards.isNumericNode)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          case 'vd':
	            // rule matches anything other than a ConstantNode or unaryMinus-wrapped ConstantNode
	            if (!(0, _wildcards.isNumericNode)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          case 'ce':
	            // rule matches expressions that have a constant value
	            if ((0, _wildcards.isConstantExpression)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          case 've':
	            // rule matches expressions that do not have a constant value
	            if (!(0, _wildcards.isConstantExpression)(node)) {
	              res[0].placeholders[rule.name] = node;
	            } else {
	              // mis-match: rule does not encompass current node
	              return [];
	            }
	            break;
	          default:
	            throw new Error('Invalid symbol in rule: ' + rule.name);
	        }
	      }
	    } else if (rule instanceof ConstantNode) {
	      // Literal constant must match exactly
	      if (!equal(rule.value, node.value)) {
	        return [];
	      }
	    } else {
	      // Some other node was encountered which we aren't prepared for, so no match
	      return [];
	    }

	    // It's a match!

	    // console.log('_ruleMatch(' + rule.toString() + ', ' + node.toString() + ') found a match')
	    return res;
	  }

	  /**
	   * Determines whether p and q (and all their children nodes) are identical.
	   *
	   * @param {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} p
	   * @param {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} q
	   * @return {Object} Information about the match, if it exists.
	   */
	  function _exactMatch(p, q) {
	    if (p instanceof ConstantNode && q instanceof ConstantNode) {
	      if (!equal(p.value, q.value)) {
	        return false;
	      }
	    } else if (p instanceof SymbolNode && q instanceof SymbolNode) {
	      if (p.name !== q.name) {
	        return false;
	      }
	    } else if (p instanceof OperatorNode && q instanceof OperatorNode || p instanceof FunctionNode && q instanceof FunctionNode) {
	      if (p instanceof OperatorNode) {
	        if (p.op !== q.op || p.fn !== q.fn) {
	          return false;
	        }
	      } else if (p instanceof FunctionNode) {
	        if (p.name !== q.name) {
	          return false;
	        }
	      }
	      if (p.args.length !== q.args.length) {
	        return false;
	      }
	      for (let i = 0; i < p.args.length; i++) {
	        if (!_exactMatch(p.args[i], q.args[i])) {
	          return false;
	        }
	      }
	    } else {
	      return false;
	    }
	    return true;
	  }
	  return simplify;
	});
	return simplify;
}

var simplifyConstant = {};

var hasRequiredSimplifyConstant;

function requireSimplifyConstant () {
	if (hasRequiredSimplifyConstant) return simplifyConstant;
	hasRequiredSimplifyConstant = 1;

	Object.defineProperty(simplifyConstant, "__esModule", {
	  value: true
	});
	simplifyConstant.createSimplifyConstant = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	var _util = requireUtil$1();
	var _noop = requireNoop();
	const name = 'simplifyConstant';
	const dependencies = ['typed', 'config', 'mathWithTransform', 'matrix', 'isBounded', '?fraction', '?bignumber', 'AccessorNode', 'ArrayNode', 'ConstantNode', 'FunctionNode', 'IndexNode', 'ObjectNode', 'OperatorNode', 'SymbolNode'];
	simplifyConstant.createSimplifyConstant = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    mathWithTransform,
	    matrix,
	    isBounded,
	    fraction,
	    bignumber,
	    AccessorNode,
	    ArrayNode,
	    ConstantNode,
	    FunctionNode,
	    IndexNode,
	    ObjectNode,
	    OperatorNode,
	    SymbolNode
	  } = _ref;
	  const {
	    isCommutative,
	    isAssociative,
	    allChildren,
	    createMakeNodeFunction
	  } = (0, _util.createUtil)({
	    FunctionNode,
	    OperatorNode,
	    SymbolNode
	  });

	  /**
	   * simplifyConstant() takes a mathjs expression (either a Node representing
	   * a parse tree or a string which it parses to produce a node), and replaces
	   * any subexpression of it consisting entirely of constants with the computed
	   * value of that subexpression.
	   *
	   * Syntax:
	   *
	   *     math.simplifyConstant(expr)
	   *     math.simplifyConstant(expr, options)
	   *
	   * Examples:
	   *
	   *     math.simplifyConstant('x + 4*3/6')  // Node "x + 2"
	   *     math.simplifyConstant('z cos(0)')   // Node "z 1"
	   *     math.simplifyConstant('(5.2 + 1.08)t', {exactFractions: false})  // Node "6.28 t"
	   *
	   * See also:
	   *
	   *     simplify, simplifyCore, resolve, derivative
	   *
	   * @param {Node | string} node
	   *     The expression to be simplified
	   * @param {Object} options
	   *     Simplification options, as per simplify()
	   * @return {Node} Returns expression with constant subexpressions evaluated
	   */
	  const simplifyConstant = typed('simplifyConstant', {
	    Node: node => _ensureNode(foldFraction(node, {})),
	    'Node, Object': function (expr, options) {
	      return _ensureNode(foldFraction(expr, options));
	    }
	  });
	  function _removeFractions(thing) {
	    if ((0, _is.isFraction)(thing)) {
	      return thing.valueOf();
	    }
	    if (thing instanceof Array) {
	      return thing.map(_removeFractions);
	    }
	    if ((0, _is.isMatrix)(thing)) {
	      return matrix(_removeFractions(thing.valueOf()));
	    }
	    return thing;
	  }
	  function _eval(fnname, args, options) {
	    try {
	      return mathWithTransform[fnname].apply(null, args);
	    } catch (ignore) {
	      // sometimes the implicit type conversion causes the evaluation to fail, so we'll try again after removing Fractions
	      args = args.map(_removeFractions);
	      return _toNumber(mathWithTransform[fnname].apply(null, args), options);
	    }
	  }
	  const _toNode = typed({
	    Fraction: _fractionToNode,
	    number: function (n) {
	      if (n < 0) {
	        return unaryMinusNode(new ConstantNode(-n));
	      }
	      return new ConstantNode(n);
	    },
	    BigNumber: function (n) {
	      if (n < 0) {
	        return unaryMinusNode(new ConstantNode(-n));
	      }
	      return new ConstantNode(n); // old parameters: (n.toString(), 'number')
	    },
	    bigint: function (n) {
	      if (n < 0n) {
	        return unaryMinusNode(new ConstantNode(-n));
	      }
	      return new ConstantNode(n);
	    },
	    Complex: function (s) {
	      throw new Error('Cannot convert Complex number to Node');
	    },
	    string: function (s) {
	      return new ConstantNode(s);
	    },
	    Matrix: function (m) {
	      return new ArrayNode(m.valueOf().map(e => _toNode(e)));
	    }
	  });
	  function _ensureNode(thing) {
	    if ((0, _is.isNode)(thing)) {
	      return thing;
	    }
	    return _toNode(thing);
	  }

	  // convert a number to a fraction only if it can be expressed exactly,
	  // and when both numerator and denominator are small enough
	  function _exactFraction(n, options) {
	    const exactFractions = options && options.exactFractions !== false;
	    if (exactFractions && isBounded(n) && fraction) {
	      const f = fraction(n);
	      const fractionsLimit = options && typeof options.fractionsLimit === 'number' ? options.fractionsLimit : Infinity; // no limit by default

	      if (f.valueOf() === n && f.n < fractionsLimit && f.d < fractionsLimit) {
	        return f;
	      }
	    }
	    return n;
	  }

	  // Convert numbers to a preferred number type in preference order: Fraction, number, Complex
	  // BigNumbers are left alone
	  const _toNumber = typed({
	    'string, Object': function (s, options) {
	      const numericType = (0, _number.safeNumberType)(s, config);
	      if (numericType === 'BigNumber') {
	        if (bignumber === undefined) {
	          (0, _noop.noBignumber)();
	        }
	        return bignumber(s);
	      } else if (numericType === 'bigint') {
	        return BigInt(s);
	      } else if (numericType === 'Fraction') {
	        if (fraction === undefined) {
	          (0, _noop.noFraction)();
	        }
	        return fraction(s);
	      } else {
	        const n = parseFloat(s);
	        return _exactFraction(n, options);
	      }
	    },
	    'Fraction, Object': function (s, options) {
	      return s;
	    },
	    // we don't need options here

	    'BigNumber, Object': function (s, options) {
	      return s;
	    },
	    // we don't need options here

	    'number, Object': function (s, options) {
	      return _exactFraction(s, options);
	    },
	    'bigint, Object': function (s, options) {
	      return s;
	    },
	    'Complex, Object': function (s, options) {
	      if (s.im !== 0) {
	        return s;
	      }
	      return _exactFraction(s.re, options);
	    },
	    'Matrix, Object': function (s, options) {
	      return matrix(_exactFraction(s.valueOf()));
	    },
	    'Array, Object': function (s, options) {
	      return s.map(_exactFraction);
	    }
	  });
	  function unaryMinusNode(n) {
	    return new OperatorNode('-', 'unaryMinus', [n]);
	  }
	  function _fractionToNode(f) {
	    // note: we convert await from bigint values, because bigint values gives issues with divisions: 1n/2n=0n and not 0.5
	    const fromBigInt = value => config.number === 'BigNumber' && bignumber ? bignumber(value) : Number(value);
	    const numeratorValue = f.s * f.n;
	    const numeratorNode = numeratorValue < 0n ? new OperatorNode('-', 'unaryMinus', [new ConstantNode(-fromBigInt(numeratorValue))]) : new ConstantNode(fromBigInt(numeratorValue));
	    return f.d === 1n ? numeratorNode : new OperatorNode('/', 'divide', [numeratorNode, new ConstantNode(fromBigInt(f.d))]);
	  }

	  /* Handles constant indexing of ArrayNodes, matrices, and ObjectNodes */
	  function _foldAccessor(obj, index, options) {
	    if (!(0, _is.isIndexNode)(index)) {
	      // don't know what to do with that...
	      return new AccessorNode(_ensureNode(obj), _ensureNode(index));
	    }
	    if ((0, _is.isArrayNode)(obj) || (0, _is.isMatrix)(obj)) {
	      const remainingDims = Array.from(index.dimensions);
	      /* We will resolve constant indices one at a time, looking
	       * just in the first or second dimensions because (a) arrays
	       * of more than two dimensions are likely rare, and (b) pulling
	       * out the third or higher dimension would be pretty intricate.
	       * The price is that we miss simplifying [..3d array][x,y,1]
	       */
	      while (remainingDims.length > 0) {
	        if ((0, _is.isConstantNode)(remainingDims[0]) && typeof remainingDims[0].value !== 'string') {
	          const first = _toNumber(remainingDims.shift().value, options);
	          if ((0, _is.isArrayNode)(obj)) {
	            obj = obj.items[first - 1];
	          } else {
	            // matrix
	            obj = obj.valueOf()[first - 1];
	            if (obj instanceof Array) {
	              obj = matrix(obj);
	            }
	          }
	        } else if (remainingDims.length > 1 && (0, _is.isConstantNode)(remainingDims[1]) && typeof remainingDims[1].value !== 'string') {
	          const second = _toNumber(remainingDims[1].value, options);
	          const tryItems = [];
	          const fromItems = (0, _is.isArrayNode)(obj) ? obj.items : obj.valueOf();
	          for (const item of fromItems) {
	            if ((0, _is.isArrayNode)(item)) {
	              tryItems.push(item.items[second - 1]);
	            } else if ((0, _is.isMatrix)(obj)) {
	              tryItems.push(item[second - 1]);
	            } else {
	              break;
	            }
	          }
	          if (tryItems.length === fromItems.length) {
	            if ((0, _is.isArrayNode)(obj)) {
	              obj = new ArrayNode(tryItems);
	            } else {
	              // matrix
	              obj = matrix(tryItems);
	            }
	            remainingDims.splice(1, 1);
	          } else {
	            // extracting slice along 2nd dimension failed, give up
	            break;
	          }
	        } else {
	          // neither 1st or 2nd dimension is constant, give up
	          break;
	        }
	      }
	      if (remainingDims.length === index.dimensions.length) {
	        /* No successful constant indexing */
	        return new AccessorNode(_ensureNode(obj), index);
	      }
	      if (remainingDims.length > 0) {
	        /* Indexed some but not all dimensions */
	        index = new IndexNode(remainingDims);
	        return new AccessorNode(_ensureNode(obj), index);
	      }
	      /* All dimensions were constant, access completely resolved */
	      return obj;
	    }
	    if ((0, _is.isObjectNode)(obj) && index.dimensions.length === 1 && (0, _is.isConstantNode)(index.dimensions[0])) {
	      const key = index.dimensions[0].value;
	      if (key in obj.properties) {
	        return obj.properties[key];
	      }
	      return new ConstantNode(); // undefined
	    }
	    /* Don't know how to index this sort of obj, at least not with this index */
	    return new AccessorNode(_ensureNode(obj), index);
	  }

	  /*
	   * Create a binary tree from a list of Fractions and Nodes.
	   * Tries to fold Fractions by evaluating them until the first Node in the list is hit, so
	   * `args` should be sorted to have the Fractions at the start (if the operator is commutative).
	   * @param args - list of Fractions and Nodes
	   * @param fn - evaluator for the binary operation evaluator that accepts two Fractions
	   * @param makeNode - creates a binary OperatorNode/FunctionNode from a list of child Nodes
	   * if args.length is 1, returns args[0]
	   * @return - Either a Node representing a binary expression or Fraction
	   */
	  function foldOp(fn, args, makeNode, options) {
	    const first = args.shift();

	    // In the following reduction, sofar always has one of the three following
	    // forms: [NODE], [CONSTANT], or [NODE, CONSTANT]
	    const reduction = args.reduce((sofar, next) => {
	      if (!(0, _is.isNode)(next)) {
	        const last = sofar.pop();
	        if ((0, _is.isNode)(last)) {
	          return [last, next];
	        }
	        // Two constants in a row, try to fold them into one
	        try {
	          sofar.push(_eval(fn, [last, next], options));
	          return sofar;
	        } catch (ignoreandcontinue) {
	          sofar.push(last);
	          // fall through to Node case
	        }
	      }

	      // Encountered a Node, or failed folding --
	      // collapse everything so far into a single tree:
	      sofar.push(_ensureNode(sofar.pop()));
	      const newtree = sofar.length === 1 ? sofar[0] : makeNode(sofar);
	      return [makeNode([newtree, _ensureNode(next)])];
	    }, [first]);
	    if (reduction.length === 1) {
	      return reduction[0];
	    }
	    // Might end up with a tree and a constant at the end:
	    return makeNode([reduction[0], _toNode(reduction[1])]);
	  }

	  // destroys the original node and returns a folded one
	  function foldFraction(node, options) {
	    switch (node.type) {
	      case 'SymbolNode':
	        return node;
	      case 'ConstantNode':
	        switch (typeof node.value) {
	          case 'number':
	            return _toNumber(node.value, options);
	          case 'bigint':
	            return _toNumber(node.value, options);
	          case 'string':
	            return node.value;
	          default:
	            if (!isNaN(node.value)) return _toNumber(node.value, options);
	        }
	        return node;
	      case 'FunctionNode':
	        if (mathWithTransform[node.name] && mathWithTransform[node.name].rawArgs) {
	          return node;
	        }
	        {
	          // Process operators as OperatorNode
	          const operatorFunctions = ['add', 'multiply'];
	          if (!operatorFunctions.includes(node.name)) {
	            const args = node.args.map(arg => foldFraction(arg, options));

	            // If all args are numbers
	            if (!args.some(_is.isNode)) {
	              try {
	                return _eval(node.name, args, options);
	              } catch (ignoreandcontinue) {}
	            }

	            // Size of a matrix does not depend on entries
	            if (node.name === 'size' && args.length === 1 && (0, _is.isArrayNode)(args[0])) {
	              const sz = [];
	              let section = args[0];
	              while ((0, _is.isArrayNode)(section)) {
	                sz.push(section.items.length);
	                section = section.items[0];
	              }
	              return matrix(sz);
	            }

	            // Convert all args to nodes and construct a symbolic function call
	            return new FunctionNode(node.name, args.map(_ensureNode));
	          }
	        }
	      /* falls through */
	      case 'OperatorNode':
	        {
	          const fn = node.fn.toString();
	          let args;
	          let res;
	          const makeNode = createMakeNodeFunction(node);
	          if ((0, _is.isOperatorNode)(node) && node.isUnary()) {
	            args = [foldFraction(node.args[0], options)];
	            if (!(0, _is.isNode)(args[0])) {
	              res = _eval(fn, args, options);
	            } else {
	              res = makeNode(args);
	            }
	          } else if (isAssociative(node, options.context)) {
	            args = allChildren(node, options.context);
	            args = args.map(arg => foldFraction(arg, options));
	            if (isCommutative(fn, options.context)) {
	              // commutative binary operator
	              const consts = [];
	              const vars = [];
	              for (let i = 0; i < args.length; i++) {
	                if (!(0, _is.isNode)(args[i])) {
	                  consts.push(args[i]);
	                } else {
	                  vars.push(args[i]);
	                }
	              }
	              if (consts.length > 1) {
	                res = foldOp(fn, consts, makeNode, options);
	                vars.unshift(res);
	                res = foldOp(fn, vars, makeNode, options);
	              } else {
	                // we won't change the children order since it's not neccessary
	                res = foldOp(fn, args, makeNode, options);
	              }
	            } else {
	              // non-commutative binary operator
	              res = foldOp(fn, args, makeNode, options);
	            }
	          } else {
	            // non-associative binary operator
	            args = node.args.map(arg => foldFraction(arg, options));
	            res = foldOp(fn, args, makeNode, options);
	          }
	          return res;
	        }
	      case 'ParenthesisNode':
	        // remove the uneccessary parenthesis
	        return foldFraction(node.content, options);
	      case 'AccessorNode':
	        return _foldAccessor(foldFraction(node.object, options), foldFraction(node.index, options), options);
	      case 'ArrayNode':
	        {
	          const foldItems = node.items.map(item => foldFraction(item, options));
	          if (foldItems.some(_is.isNode)) {
	            return new ArrayNode(foldItems.map(_ensureNode));
	          }
	          /* All literals -- return a Matrix so we can operate on it */
	          return matrix(foldItems);
	        }
	      case 'IndexNode':
	        {
	          return new IndexNode(node.dimensions.map(n => simplifyConstant(n, options)));
	        }
	      case 'ObjectNode':
	        {
	          const foldProps = {};
	          for (const prop in node.properties) {
	            foldProps[prop] = simplifyConstant(node.properties[prop], options);
	          }
	          return new ObjectNode(foldProps);
	        }
	      case 'AssignmentNode':
	      /* falls through */
	      case 'BlockNode':
	      /* falls through */
	      case 'FunctionAssignmentNode':
	      /* falls through */
	      case 'RangeNode':
	      /* falls through */
	      case 'ConditionalNode':
	      /* falls through */
	      default:
	        throw new Error(`Unimplemented node type in simplifyConstant: ${node.type}`);
	    }
	  }
	  return simplifyConstant;
	});
	return simplifyConstant;
}

var simplifyCore = {};

var hasRequiredSimplifyCore;

function requireSimplifyCore () {
	if (hasRequiredSimplifyCore) return simplifyCore;
	hasRequiredSimplifyCore = 1;

	Object.defineProperty(simplifyCore, "__esModule", {
	  value: true
	});
	simplifyCore.createSimplifyCore = void 0;
	var _is = requireIs();
	var _operators = requireOperators();
	var _util = requireUtil$1();
	var _factory = requireFactory$b();
	const name = 'simplifyCore';
	const dependencies = ['typed', 'parse', 'equal', 'isZero', 'add', 'subtract', 'multiply', 'divide', 'pow', 'AccessorNode', 'ArrayNode', 'ConstantNode', 'FunctionNode', 'IndexNode', 'ObjectNode', 'OperatorNode', 'ParenthesisNode', 'SymbolNode'];
	simplifyCore.createSimplifyCore = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    parse,
	    equal,
	    isZero,
	    add,
	    subtract,
	    multiply,
	    divide,
	    pow,
	    AccessorNode,
	    ArrayNode,
	    ConstantNode,
	    FunctionNode,
	    IndexNode,
	    ObjectNode,
	    OperatorNode,
	    ParenthesisNode,
	    SymbolNode
	  } = _ref;
	  const node0 = new ConstantNode(0);
	  const node1 = new ConstantNode(1);
	  const nodeT = new ConstantNode(true);
	  const nodeF = new ConstantNode(false);
	  // test if a node will always have a boolean value (true/false)
	  // not sure if this list is complete
	  function isAlwaysBoolean(node) {
	    return (0, _is.isOperatorNode)(node) && ['and', 'not', 'or'].includes(node.op);
	  }
	  const {
	    hasProperty,
	    isCommutative
	  } = (0, _util.createUtil)({
	    FunctionNode,
	    OperatorNode,
	    SymbolNode
	  });
	  /**
	   * simplifyCore() performs single pass simplification suitable for
	   * applications requiring ultimate performance. To roughly summarize,
	   * it handles cases along the lines of simplifyConstant() but where
	   * knowledge of a single argument is sufficient to determine the value.
	   * In contrast, simplify() extends simplifyCore() with additional passes
	   * to provide deeper simplification (such as gathering like terms).
	   *
	   * Specifically, simplifyCore:
	   *
	   * * Converts all function calls with operator equivalents to their
	   *   operator forms.
	   * * Removes operators or function calls that are guaranteed to have no
	   *   effect (such as unary '+').
	   * * Removes double unary '-', '~', and 'not'
	   * * Eliminates addition/subtraction of 0 and multiplication/division/powers
	   *   by 1 or 0.
	   * * Converts addition of a negation into subtraction.
	   * * Eliminates logical operations with constant true or false leading
	   *   arguments.
	   * * Puts constants on the left of a product, if multiplication is
	   *   considered commutative by the options (which is the default)
	   *
	   * Syntax:
	   *
	   *     math.simplifyCore(expr)
	   *     math.simplifyCore(expr, options)
	   *
	   * Examples:
	   *
	   *     const f = math.parse('2 * 1 * x ^ (1 - 0)')
	   *     math.simplifyCore(f)                          // Node "2 * x"
	   *     math.simplify('2 * 1 * x ^ (1 - 0)', [math.simplifyCore]) // Node "2 * x"
	   *
	   * See also:
	   *
	   *     simplify, simplifyConstant, resolve, derivative
	   *
	   * @param {Node | string} node
	   *     The expression to be simplified
	   * @param {Object} options
	   *     Simplification options, as per simplify()
	   * @return {Node} Returns expression with basic simplifications applied
	   */
	  function _simplifyCore(nodeToSimplify) {
	    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	    const context = options ? options.context : undefined;
	    if (hasProperty(nodeToSimplify, 'trivial', context)) {
	      // This node does nothing if it has only one argument, so if so,
	      // return that argument simplified
	      if ((0, _is.isFunctionNode)(nodeToSimplify) && nodeToSimplify.args.length === 1) {
	        return _simplifyCore(nodeToSimplify.args[0], options);
	      }
	      // For other node types, we try the generic methods
	      let simpChild = false;
	      let childCount = 0;
	      nodeToSimplify.forEach(c => {
	        ++childCount;
	        if (childCount === 1) {
	          simpChild = _simplifyCore(c, options);
	        }
	      });
	      if (childCount === 1) {
	        return simpChild;
	      }
	    }
	    let node = nodeToSimplify;
	    if ((0, _is.isFunctionNode)(node)) {
	      const op = (0, _operators.getOperator)(node.name);
	      if (op) {
	        // Replace FunctionNode with a new OperatorNode
	        if (node.args.length > 2 && hasProperty(node, 'associative', context)) {
	          // unflatten into binary operations since that's what simplifyCore handles
	          while (node.args.length > 2) {
	            const last = node.args.pop();
	            const seclast = node.args.pop();
	            node.args.push(new OperatorNode(op, node.name, [last, seclast]));
	          }
	        }
	        node = new OperatorNode(op, node.name, node.args);
	      } else {
	        return new FunctionNode(_simplifyCore(node.fn), node.args.map(n => _simplifyCore(n, options)));
	      }
	    }
	    if ((0, _is.isOperatorNode)(node) && node.isUnary()) {
	      const a0 = _simplifyCore(node.args[0], options);
	      if (node.op === '~') {
	        // bitwise not
	        if ((0, _is.isOperatorNode)(a0) && a0.isUnary() && a0.op === '~') {
	          return a0.args[0];
	        }
	      }
	      if (node.op === 'not') {
	        // logical not
	        if ((0, _is.isOperatorNode)(a0) && a0.isUnary() && a0.op === 'not') {
	          // Has the effect of turning the argument into a boolean
	          // So can only eliminate the double negation if
	          // the inside is already boolean
	          if (isAlwaysBoolean(a0.args[0])) {
	            return a0.args[0];
	          }
	        }
	      }
	      let finish = true;
	      if (node.op === '-') {
	        // unary minus
	        if ((0, _is.isOperatorNode)(a0)) {
	          if (a0.isBinary() && a0.fn === 'subtract') {
	            node = new OperatorNode('-', 'subtract', [a0.args[1], a0.args[0]]);
	            finish = false; // continue to process the new binary node
	          }
	          if (a0.isUnary() && a0.op === '-') {
	            return a0.args[0];
	          }
	        }
	      }
	      if (finish) return new OperatorNode(node.op, node.fn, [a0]);
	    }
	    if ((0, _is.isOperatorNode)(node) && node.isBinary()) {
	      const a0 = _simplifyCore(node.args[0], options);
	      let a1 = _simplifyCore(node.args[1], options);
	      if (node.op === '+') {
	        if ((0, _is.isConstantNode)(a0) && isZero(a0.value)) {
	          return a1;
	        }
	        if ((0, _is.isConstantNode)(a1) && isZero(a1.value)) {
	          return a0;
	        }
	        if ((0, _is.isOperatorNode)(a1) && a1.isUnary() && a1.op === '-') {
	          a1 = a1.args[0];
	          node = new OperatorNode('-', 'subtract', [a0, a1]);
	        }
	      }
	      if (node.op === '-') {
	        if ((0, _is.isOperatorNode)(a1) && a1.isUnary() && a1.op === '-') {
	          return _simplifyCore(new OperatorNode('+', 'add', [a0, a1.args[0]]), options);
	        }
	        if ((0, _is.isConstantNode)(a0) && isZero(a0.value)) {
	          return _simplifyCore(new OperatorNode('-', 'unaryMinus', [a1]));
	        }
	        if ((0, _is.isConstantNode)(a1) && isZero(a1.value)) {
	          return a0;
	        }
	        return new OperatorNode(node.op, node.fn, [a0, a1]);
	      }
	      if (node.op === '*') {
	        if ((0, _is.isConstantNode)(a0)) {
	          if (isZero(a0.value)) {
	            return node0;
	          } else if (equal(a0.value, 1)) {
	            return a1;
	          }
	        }
	        if ((0, _is.isConstantNode)(a1)) {
	          if (isZero(a1.value)) {
	            return node0;
	          } else if (equal(a1.value, 1)) {
	            return a0;
	          }
	          if (isCommutative(node, context)) {
	            return new OperatorNode(node.op, node.fn, [a1, a0], node.implicit); // constants on left
	          }
	        }
	        return new OperatorNode(node.op, node.fn, [a0, a1], node.implicit);
	      }
	      if (node.op === '/') {
	        if ((0, _is.isConstantNode)(a0) && isZero(a0.value)) {
	          return node0;
	        }
	        if ((0, _is.isConstantNode)(a1) && equal(a1.value, 1)) {
	          return a0;
	        }
	        return new OperatorNode(node.op, node.fn, [a0, a1]);
	      }
	      if (node.op === '^') {
	        if ((0, _is.isConstantNode)(a1)) {
	          if (isZero(a1.value)) {
	            return node1;
	          } else if (equal(a1.value, 1)) {
	            return a0;
	          }
	        }
	      }
	      if (node.op === 'and') {
	        if ((0, _is.isConstantNode)(a0)) {
	          if (a0.value) {
	            if (isAlwaysBoolean(a1)) return a1;
	            if ((0, _is.isConstantNode)(a1)) {
	              return a1.value ? nodeT : nodeF;
	            }
	          } else {
	            return nodeF;
	          }
	        }
	        if ((0, _is.isConstantNode)(a1)) {
	          if (a1.value) {
	            if (isAlwaysBoolean(a0)) return a0;
	          } else {
	            return nodeF;
	          }
	        }
	      }
	      if (node.op === 'or') {
	        if ((0, _is.isConstantNode)(a0)) {
	          if (a0.value) {
	            return nodeT;
	          } else {
	            if (isAlwaysBoolean(a1)) return a1;
	          }
	        }
	        if ((0, _is.isConstantNode)(a1)) {
	          if (a1.value) {
	            return nodeT;
	          } else {
	            if (isAlwaysBoolean(a0)) return a0;
	          }
	        }
	      }
	      return new OperatorNode(node.op, node.fn, [a0, a1]);
	    }
	    if ((0, _is.isOperatorNode)(node)) {
	      return new OperatorNode(node.op, node.fn, node.args.map(a => _simplifyCore(a, options)));
	    }
	    if ((0, _is.isArrayNode)(node)) {
	      return new ArrayNode(node.items.map(n => _simplifyCore(n, options)));
	    }
	    if ((0, _is.isAccessorNode)(node)) {
	      return new AccessorNode(_simplifyCore(node.object, options), _simplifyCore(node.index, options));
	    }
	    if ((0, _is.isIndexNode)(node)) {
	      return new IndexNode(node.dimensions.map(n => _simplifyCore(n, options)));
	    }
	    if ((0, _is.isObjectNode)(node)) {
	      const newProps = {};
	      for (const prop in node.properties) {
	        newProps[prop] = _simplifyCore(node.properties[prop], options);
	      }
	      return new ObjectNode(newProps);
	    }
	    // cannot simplify
	    return node;
	  }
	  return typed(name, {
	    Node: _simplifyCore,
	    'Node,Object': _simplifyCore
	  });
	});
	return simplifyCore;
}

var resolve = {};

var hasRequiredResolve;

function requireResolve () {
	if (hasRequiredResolve) return resolve;
	hasRequiredResolve = 1;

	Object.defineProperty(resolve, "__esModule", {
	  value: true
	});
	resolve.createResolve = void 0;
	var _map = requireMap$2();
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'resolve';
	const dependencies = ['typed', 'parse', 'ConstantNode', 'FunctionNode', 'OperatorNode', 'ParenthesisNode'];
	resolve.createResolve = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    parse,
	    ConstantNode,
	    FunctionNode,
	    OperatorNode,
	    ParenthesisNode
	  } = _ref;
	  /**
	   * resolve(expr, scope) replaces variable nodes with their scoped values
	   *
	   * Syntax:
	   *
	   *     math.resolve(expr, scope)
	   *
	   * Examples:
	   *
	   *     math.resolve('x + y', {x:1, y:2})           // Node '1 + 2'
	   *     math.resolve(math.parse('x+y'), {x:1, y:2}) // Node '1 + 2'
	   *     math.simplify('x+y', {x:2, y: math.parse('x+x')}).toString() // "6"
	   *
	   * See also:
	   *
	   *     simplify, evaluate
	   *
	   * @param {Node | Node[]} node
	   *     The expression tree (or trees) to be simplified
	   * @param {Object} scope
	   *     Scope specifying variables to be resolved
	   * @return {Node | Node[]} Returns `node` with variables recursively substituted.
	   * @throws {ReferenceError}
	   *     If there is a cyclic dependency among the variables in `scope`,
	   *     resolution is impossible and a ReferenceError is thrown.
	   */
	  function _resolve(node, scope) {
	    let within = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new Set();
	    // note `within`:
	    // `within` is not documented, since it is for internal cycle
	    // detection only
	    if (!scope) {
	      return node;
	    }
	    if ((0, _is.isSymbolNode)(node)) {
	      if (within.has(node.name)) {
	        const variables = Array.from(within).join(', ');
	        throw new ReferenceError(`recursive loop of variable definitions among {${variables}}`);
	      }
	      const value = scope.get(node.name);
	      if ((0, _is.isNode)(value)) {
	        const nextWithin = new Set(within);
	        nextWithin.add(node.name);
	        return _resolve(value, scope, nextWithin);
	      } else if (typeof value === 'number') {
	        return parse(String(value));
	      } else if (value !== undefined) {
	        return new ConstantNode(value);
	      } else {
	        return node;
	      }
	    } else if ((0, _is.isOperatorNode)(node)) {
	      const args = node.args.map(function (arg) {
	        return _resolve(arg, scope, within);
	      });
	      return new OperatorNode(node.op, node.fn, args, node.implicit);
	    } else if ((0, _is.isParenthesisNode)(node)) {
	      return new ParenthesisNode(_resolve(node.content, scope, within));
	    } else if ((0, _is.isFunctionNode)(node)) {
	      const args = node.args.map(function (arg) {
	        return _resolve(arg, scope, within);
	      });
	      return new FunctionNode(node.name, args);
	    }

	    // Otherwise just recursively resolve any children (might also work
	    // for some of the above special cases)
	    return node.map(child => _resolve(child, scope, within));
	  }
	  return typed('resolve', {
	    Node: _resolve,
	    'Node, Map | null | undefined': _resolve,
	    'Node, Object': (n, scope) => _resolve(n, (0, _map.createMap)(scope)),
	    // For arrays and matrices, we map `self` rather than `_resolve`
	    // because resolve is fairly expensive anyway, and this way
	    // we get nice error messages if one entry in the array has wrong type.
	    'Array | Matrix': typed.referToSelf(self => A => A.map(n => self(n))),
	    'Array | Matrix, null | undefined': typed.referToSelf(self => A => A.map(n => self(n))),
	    'Array, Object': typed.referTo('Array,Map', selfAM => (A, scope) => selfAM(A, (0, _map.createMap)(scope))),
	    'Matrix, Object': typed.referTo('Matrix,Map', selfMM => (A, scope) => selfMM(A, (0, _map.createMap)(scope))),
	    'Array | Matrix, Map': typed.referToSelf(self => (A, scope) => A.map(n => self(n, scope)))
	  });
	});
	return resolve;
}

var symbolicEqual = {};

var hasRequiredSymbolicEqual;

function requireSymbolicEqual () {
	if (hasRequiredSymbolicEqual) return symbolicEqual;
	hasRequiredSymbolicEqual = 1;

	Object.defineProperty(symbolicEqual, "__esModule", {
	  value: true
	});
	symbolicEqual.createSymbolicEqual = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'symbolicEqual';
	const dependencies = ['parse', 'simplify', 'typed', 'OperatorNode'];
	symbolicEqual.createSymbolicEqual = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    parse,
	    simplify,
	    typed,
	    OperatorNode
	  } = _ref;
	  /**
	   * Attempts to determine if two expressions are symbolically equal, i.e.
	   * one is the result of valid algebraic manipulations on the other.
	   * Currently, this simply checks if the difference of the two expressions
	   * simplifies down to 0. So there are two important caveats:
	   * 1. whether two expressions are symbolically equal depends on the
	   *     manipulations allowed. Therefore, this function takes an optional
	   *     third argument, which are the options that control the behavior
	   *     as documented for the `simplify()` function.
	   * 2. it is in general intractable to find the minimal simplification of
	   *     an arbitrarily complicated expression. So while a `true` value
	   *     of `symbolicEqual` ensures that the two expressions can be manipulated
	   *     to match each other, a `false` value does not absolutely rule this out.
	   *
	   * Syntax:
	   *
	   *     math.symbolicEqual(expr1, expr2)
	   *     math.symbolicEqual(expr1, expr2, options)
	   *
	   * Examples:
	   *
	   *     math.symbolicEqual('x*y', 'y*x') // Returns true
	   *     math.symbolicEqual('x*y', 'y*x', {context: {multiply: {commutative: false}}}) // Returns false
	   *     math.symbolicEqual('x/y', '(y*x^(-1))^(-1)') // Returns true
	   *     math.symbolicEqual('abs(x)','x') // Returns false
	   *     math.symbolicEqual('abs(x)','x', simplify.positiveContext) // Returns true
	   *
	   * See also:
	   *
	   *     simplify, evaluate
	   *
	   * @param {Node|string} expr1  The first expression to compare
	   * @param {Node|string} expr2  The second expression to compare
	   * @param {Object} [options] Optional option object, passed to simplify
	   * @returns {boolean}
	   *     Returns true if a valid manipulation making the expressions equal
	   *     is found.
	   */
	  function _symbolicEqual(e1, e2) {
	    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
	    const diff = new OperatorNode('-', 'subtract', [e1, e2]);
	    const simplified = simplify(diff, {}, options);
	    return (0, _is.isConstantNode)(simplified) && !simplified.value;
	  }
	  return typed(name, {
	    'Node, Node': _symbolicEqual,
	    'Node, Node, Object': _symbolicEqual
	  });
	});
	return symbolicEqual;
}

var derivative = {};

var hasRequiredDerivative;

function requireDerivative () {
	if (hasRequiredDerivative) return derivative;
	hasRequiredDerivative = 1;

	Object.defineProperty(derivative, "__esModule", {
	  value: true
	});
	derivative.createDerivative = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _number = requireNumber$3();
	const name = 'derivative';
	const dependencies = ['typed', 'config', 'parse', 'simplify', 'equal', 'isZero', 'numeric', 'ConstantNode', 'FunctionNode', 'OperatorNode', 'ParenthesisNode', 'SymbolNode'];
	derivative.createDerivative = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    parse,
	    simplify,
	    equal,
	    isZero,
	    numeric,
	    ConstantNode,
	    FunctionNode,
	    OperatorNode,
	    ParenthesisNode,
	    SymbolNode
	  } = _ref;
	  /**
	   * Takes the derivative of an expression expressed in parser Nodes.
	   * The derivative will be taken over the supplied variable in the
	   * second parameter. If there are multiple variables in the expression,
	   * it will return a partial derivative.
	   *
	   * This uses rules of differentiation which can be found here:
	   *
	   * - [Differentiation rules (Wikipedia)](https://en.wikipedia.org/wiki/Differentiation_rules)
	   *
	   * Syntax:
	   *
	   *     math.derivative(expr, variable)
	   *     math.derivative(expr, variable, options)
	   *
	   * Examples:
	   *
	   *     math.derivative('x^2', 'x')                     // Node '2 * x'
	   *     math.derivative('x^2', 'x', {simplify: false})  // Node '2 * 1 * x ^ (2 - 1)'
	   *     math.derivative('sin(2x)', 'x'))                // Node '2 * cos(2 * x)'
	   *     math.derivative('2*x', 'x').evaluate()          // number 2
	   *     math.derivative('x^2', 'x').evaluate({x: 4})    // number 8
	   *     const f = math.parse('x^2')
	   *     const x = math.parse('x')
	   *     math.derivative(f, x)                           // Node {2 * x}
	   *
	   * See also:
	   *
	   *     simplify, parse, evaluate
	   *
	   * @param  {Node | string} expr           The expression to differentiate
	   * @param  {SymbolNode | string} variable The variable over which to differentiate
	   * @param  {{simplify: boolean}} [options]
	   *                         There is one option available, `simplify`, which
	   *                         is true by default. When false, output will not
	   *                         be simplified.
	   * @return {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode}    The derivative of `expr`
	   */
	  function plainDerivative(expr, variable) {
	    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
	      simplify: true
	    };
	    const cache = new Map();
	    const variableName = variable.name;
	    function isConstCached(node) {
	      const cached = cache.get(node);
	      if (cached !== undefined) {
	        return cached;
	      }
	      const res = _isConst(isConstCached, node, variableName);
	      cache.set(node, res);
	      return res;
	    }
	    const res = _derivative(expr, isConstCached);
	    return options.simplify ? simplify(res) : res;
	  }
	  function parseIdentifier(string) {
	    const symbol = parse(string);
	    if (!symbol.isSymbolNode) {
	      throw new TypeError('Invalid variable. ' + `Cannot parse ${JSON.stringify(string)} into a variable in function derivative`);
	    }
	    return symbol;
	  }
	  const derivative = typed(name, {
	    'Node, SymbolNode': plainDerivative,
	    'Node, SymbolNode, Object': plainDerivative,
	    'Node, string': (node, symbol) => plainDerivative(node, parseIdentifier(symbol)),
	    'Node, string, Object': (node, symbol, options) => plainDerivative(node, parseIdentifier(symbol), options)

	    /* TODO: implement and test syntax with order of derivatives -> implement as an option {order: number}
	    'Node, SymbolNode, ConstantNode': function (expr, variable, {order}) {
	      let res = expr
	      for (let i = 0; i < order; i++) {
	        <create caching isConst>
	        res = _derivative(res, isConst)
	      }
	      return res
	    }
	    */
	  });
	  derivative._simplify = true;
	  derivative.toTex = function (deriv) {
	    return _derivTex.apply(null, deriv.args);
	  };

	  // FIXME: move the toTex method of derivative to latex.js. Difficulty is that it relies on parse.
	  // NOTE: the optional "order" parameter here is currently unused
	  const _derivTex = typed('_derivTex', {
	    'Node, SymbolNode': function (expr, x) {
	      if ((0, _is.isConstantNode)(expr) && (0, _is.typeOf)(expr.value) === 'string') {
	        return _derivTex(parse(expr.value).toString(), x.toString(), 1);
	      } else {
	        return _derivTex(expr.toTex(), x.toString(), 1);
	      }
	    },
	    'Node, ConstantNode': function (expr, x) {
	      if ((0, _is.typeOf)(x.value) === 'string') {
	        return _derivTex(expr, parse(x.value));
	      } else {
	        throw new Error("The second parameter to 'derivative' is a non-string constant");
	      }
	    },
	    'Node, SymbolNode, ConstantNode': function (expr, x, order) {
	      return _derivTex(expr.toString(), x.name, order.value);
	    },
	    'string, string, number': function (expr, x, order) {
	      let d;
	      if (order === 1) {
	        d = '{d\\over d' + x + '}';
	      } else {
	        d = '{d^{' + order + '}\\over d' + x + '^{' + order + '}}';
	      }
	      return d + `\\left[${expr}\\right]`;
	    }
	  });

	  /**
	   * Checks if a node is constants (e.g. 2 + 2).
	   * Accepts (usually memoized) version of self as the first parameter for recursive calls.
	   * Classification is done as follows:
	   *
	   *   1. ConstantNodes are constants.
	   *   2. If there exists a SymbolNode, of which we are differentiating over,
	   *      in the subtree it is not constant.
	   *
	   * @param  {function} isConst  Function that tells whether sub-expression is a constant
	   * @param  {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} node
	   * @param  {string} varName     Variable that we are differentiating
	   * @return {boolean}  if node is constant
	   */
	  const _isConst = typed('_isConst', {
	    'function, ConstantNode, string': function () {
	      return true;
	    },
	    'function, SymbolNode, string': function (isConst, node, varName) {
	      // Treat other variables like constants. For reasoning, see:
	      //   https://en.wikipedia.org/wiki/Partial_derivative
	      return node.name !== varName;
	    },
	    'function, ParenthesisNode, string': function (isConst, node, varName) {
	      return isConst(node.content, varName);
	    },
	    'function, FunctionAssignmentNode, string': function (isConst, node, varName) {
	      if (!node.params.includes(varName)) {
	        return true;
	      }
	      return isConst(node.expr, varName);
	    },
	    'function, FunctionNode | OperatorNode, string': function (isConst, node, varName) {
	      return node.args.every(arg => isConst(arg, varName));
	    }
	  });

	  /**
	   * Applies differentiation rules.
	   *
	   * @param  {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode} node
	   * @param  {function} isConst  Function that tells if a node is constant
	   * @return {ConstantNode | SymbolNode | ParenthesisNode | FunctionNode | OperatorNode}    The derivative of `expr`
	   */
	  const _derivative = typed('_derivative', {
	    'ConstantNode, function': function () {
	      return createConstantNode(0);
	    },
	    'SymbolNode, function': function (node, isConst) {
	      if (isConst(node)) {
	        return createConstantNode(0);
	      }
	      return createConstantNode(1);
	    },
	    'ParenthesisNode, function': function (node, isConst) {
	      return new ParenthesisNode(_derivative(node.content, isConst));
	    },
	    'FunctionAssignmentNode, function': function (node, isConst) {
	      if (isConst(node)) {
	        return createConstantNode(0);
	      }
	      return _derivative(node.expr, isConst);
	    },
	    'FunctionNode, function': function (node, isConst) {
	      if (isConst(node)) {
	        return createConstantNode(0);
	      }
	      const arg0 = node.args[0];
	      let arg1;
	      let div = false; // is output a fraction?
	      let negative = false; // is output negative?

	      let funcDerivative;
	      switch (node.name) {
	        case 'cbrt':
	          // d/dx(cbrt(x)) = 1 / (3x^(2/3))
	          div = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [createConstantNode(3), new OperatorNode('^', 'pow', [arg0, new OperatorNode('/', 'divide', [createConstantNode(2), createConstantNode(3)])])]);
	          break;
	        case 'sqrt':
	        case 'nthRoot':
	          // d/dx(sqrt(x)) = 1 / (2*sqrt(x))
	          if (node.args.length === 1) {
	            div = true;
	            funcDerivative = new OperatorNode('*', 'multiply', [createConstantNode(2), new FunctionNode('sqrt', [arg0])]);
	          } else if (node.args.length === 2) {
	            // Rearrange from nthRoot(x, a) -> x^(1/a)
	            arg1 = new OperatorNode('/', 'divide', [createConstantNode(1), node.args[1]]);
	            return _derivative(new OperatorNode('^', 'pow', [arg0, arg1]), isConst);
	          }
	          break;
	        case 'log10':
	          arg1 = createConstantNode(10);
	        /* fall through! */
	        case 'log':
	          if (!arg1 && node.args.length === 1) {
	            // d/dx(log(x)) = 1 / x
	            funcDerivative = arg0.clone();
	            div = true;
	          } else if (node.args.length === 1 && arg1 || node.args.length === 2 && isConst(node.args[1])) {
	            // d/dx(log(x, c)) = 1 / (x*ln(c))
	            funcDerivative = new OperatorNode('*', 'multiply', [arg0.clone(), new FunctionNode('log', [arg1 || node.args[1]])]);
	            div = true;
	          } else if (node.args.length === 2) {
	            // d/dx(log(f(x), g(x))) = d/dx(log(f(x)) / log(g(x)))
	            return _derivative(new OperatorNode('/', 'divide', [new FunctionNode('log', [arg0]), new FunctionNode('log', [node.args[1]])]), isConst);
	          }
	          break;
	        case 'pow':
	          if (node.args.length === 2) {
	            // Pass to pow operator node parser
	            return _derivative(new OperatorNode('^', 'pow', [arg0, node.args[1]]), isConst);
	          }
	          break;
	        case 'exp':
	          // d/dx(e^x) = e^x
	          funcDerivative = new FunctionNode('exp', [arg0.clone()]);
	          break;
	        case 'sin':
	          // d/dx(sin(x)) = cos(x)
	          funcDerivative = new FunctionNode('cos', [arg0.clone()]);
	          break;
	        case 'cos':
	          // d/dx(cos(x)) = -sin(x)
	          funcDerivative = new OperatorNode('-', 'unaryMinus', [new FunctionNode('sin', [arg0.clone()])]);
	          break;
	        case 'tan':
	          // d/dx(tan(x)) = sec(x)^2
	          funcDerivative = new OperatorNode('^', 'pow', [new FunctionNode('sec', [arg0.clone()]), createConstantNode(2)]);
	          break;
	        case 'sec':
	          // d/dx(sec(x)) = sec(x)tan(x)
	          funcDerivative = new OperatorNode('*', 'multiply', [node, new FunctionNode('tan', [arg0.clone()])]);
	          break;
	        case 'csc':
	          // d/dx(csc(x)) = -csc(x)cot(x)
	          negative = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [node, new FunctionNode('cot', [arg0.clone()])]);
	          break;
	        case 'cot':
	          // d/dx(cot(x)) = -csc(x)^2
	          negative = true;
	          funcDerivative = new OperatorNode('^', 'pow', [new FunctionNode('csc', [arg0.clone()]), createConstantNode(2)]);
	          break;
	        case 'asin':
	          // d/dx(asin(x)) = 1 / sqrt(1 - x^2)
	          div = true;
	          funcDerivative = new FunctionNode('sqrt', [new OperatorNode('-', 'subtract', [createConstantNode(1), new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)])])]);
	          break;
	        case 'acos':
	          // d/dx(acos(x)) = -1 / sqrt(1 - x^2)
	          div = true;
	          negative = true;
	          funcDerivative = new FunctionNode('sqrt', [new OperatorNode('-', 'subtract', [createConstantNode(1), new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)])])]);
	          break;
	        case 'atan':
	          // d/dx(atan(x)) = 1 / (x^2 + 1)
	          div = true;
	          funcDerivative = new OperatorNode('+', 'add', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)]);
	          break;
	        case 'asec':
	          // d/dx(asec(x)) = 1 / (|x|*sqrt(x^2 - 1))
	          div = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [new FunctionNode('abs', [arg0.clone()]), new FunctionNode('sqrt', [new OperatorNode('-', 'subtract', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)])])]);
	          break;
	        case 'acsc':
	          // d/dx(acsc(x)) = -1 / (|x|*sqrt(x^2 - 1))
	          div = true;
	          negative = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [new FunctionNode('abs', [arg0.clone()]), new FunctionNode('sqrt', [new OperatorNode('-', 'subtract', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)])])]);
	          break;
	        case 'acot':
	          // d/dx(acot(x)) = -1 / (x^2 + 1)
	          div = true;
	          negative = true;
	          funcDerivative = new OperatorNode('+', 'add', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)]);
	          break;
	        case 'sinh':
	          // d/dx(sinh(x)) = cosh(x)
	          funcDerivative = new FunctionNode('cosh', [arg0.clone()]);
	          break;
	        case 'cosh':
	          // d/dx(cosh(x)) = sinh(x)
	          funcDerivative = new FunctionNode('sinh', [arg0.clone()]);
	          break;
	        case 'tanh':
	          // d/dx(tanh(x)) = sech(x)^2
	          funcDerivative = new OperatorNode('^', 'pow', [new FunctionNode('sech', [arg0.clone()]), createConstantNode(2)]);
	          break;
	        case 'sech':
	          // d/dx(sech(x)) = -sech(x)tanh(x)
	          negative = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [node, new FunctionNode('tanh', [arg0.clone()])]);
	          break;
	        case 'csch':
	          // d/dx(csch(x)) = -csch(x)coth(x)
	          negative = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [node, new FunctionNode('coth', [arg0.clone()])]);
	          break;
	        case 'coth':
	          // d/dx(coth(x)) = -csch(x)^2
	          negative = true;
	          funcDerivative = new OperatorNode('^', 'pow', [new FunctionNode('csch', [arg0.clone()]), createConstantNode(2)]);
	          break;
	        case 'asinh':
	          // d/dx(asinh(x)) = 1 / sqrt(x^2 + 1)
	          div = true;
	          funcDerivative = new FunctionNode('sqrt', [new OperatorNode('+', 'add', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)])]);
	          break;
	        case 'acosh':
	          // d/dx(acosh(x)) = 1 / sqrt(x^2 - 1); XXX potentially only for x >= 1 (the real spectrum)
	          div = true;
	          funcDerivative = new FunctionNode('sqrt', [new OperatorNode('-', 'subtract', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)])]);
	          break;
	        case 'atanh':
	          // d/dx(atanh(x)) = 1 / (1 - x^2)
	          div = true;
	          funcDerivative = new OperatorNode('-', 'subtract', [createConstantNode(1), new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)])]);
	          break;
	        case 'asech':
	          // d/dx(asech(x)) = -1 / (x*sqrt(1 - x^2))
	          div = true;
	          negative = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [arg0.clone(), new FunctionNode('sqrt', [new OperatorNode('-', 'subtract', [createConstantNode(1), new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)])])])]);
	          break;
	        case 'acsch':
	          // d/dx(acsch(x)) = -1 / (|x|*sqrt(x^2 + 1))
	          div = true;
	          negative = true;
	          funcDerivative = new OperatorNode('*', 'multiply', [new FunctionNode('abs', [arg0.clone()]), new FunctionNode('sqrt', [new OperatorNode('+', 'add', [new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)]), createConstantNode(1)])])]);
	          break;
	        case 'acoth':
	          // d/dx(acoth(x)) = -1 / (1 - x^2)
	          div = true;
	          negative = true;
	          funcDerivative = new OperatorNode('-', 'subtract', [createConstantNode(1), new OperatorNode('^', 'pow', [arg0.clone(), createConstantNode(2)])]);
	          break;
	        case 'abs':
	          // d/dx(abs(x)) = abs(x)/x
	          funcDerivative = new OperatorNode('/', 'divide', [new FunctionNode(new SymbolNode('abs'), [arg0.clone()]), arg0.clone()]);
	          break;
	        case 'gamma': // Needs digamma function, d/dx(gamma(x)) = gamma(x)digamma(x)
	        default:
	          throw new Error('Cannot process function "' + node.name + '" in derivative: ' + 'the function is not supported, undefined, or the number of arguments passed to it are not supported');
	      }
	      let op, func;
	      if (div) {
	        op = '/';
	        func = 'divide';
	      } else {
	        op = '*';
	        func = 'multiply';
	      }

	      /* Apply chain rule to all functions:
	         F(x)  = f(g(x))
	         F'(x) = g'(x)*f'(g(x)) */
	      let chainDerivative = _derivative(arg0, isConst);
	      if (negative) {
	        chainDerivative = new OperatorNode('-', 'unaryMinus', [chainDerivative]);
	      }
	      return new OperatorNode(op, func, [chainDerivative, funcDerivative]);
	    },
	    'OperatorNode, function': function (node, isConst) {
	      if (isConst(node)) {
	        return createConstantNode(0);
	      }
	      if (node.op === '+') {
	        // d/dx(sum(f(x)) = sum(f'(x))
	        return new OperatorNode(node.op, node.fn, node.args.map(function (arg) {
	          return _derivative(arg, isConst);
	        }));
	      }
	      if (node.op === '-') {
	        // d/dx(+/-f(x)) = +/-f'(x)
	        if (node.isUnary()) {
	          return new OperatorNode(node.op, node.fn, [_derivative(node.args[0], isConst)]);
	        }

	        // Linearity of differentiation, d/dx(f(x) +/- g(x)) = f'(x) +/- g'(x)
	        if (node.isBinary()) {
	          return new OperatorNode(node.op, node.fn, [_derivative(node.args[0], isConst), _derivative(node.args[1], isConst)]);
	        }
	      }
	      if (node.op === '*') {
	        // d/dx(c*f(x)) = c*f'(x)
	        const constantTerms = node.args.filter(function (arg) {
	          return isConst(arg);
	        });
	        if (constantTerms.length > 0) {
	          const nonConstantTerms = node.args.filter(function (arg) {
	            return !isConst(arg);
	          });
	          const nonConstantNode = nonConstantTerms.length === 1 ? nonConstantTerms[0] : new OperatorNode('*', 'multiply', nonConstantTerms);
	          const newArgs = constantTerms.concat(_derivative(nonConstantNode, isConst));
	          return new OperatorNode('*', 'multiply', newArgs);
	        }

	        // Product Rule, d/dx(f(x)*g(x)) = f'(x)*g(x) + f(x)*g'(x)
	        return new OperatorNode('+', 'add', node.args.map(function (argOuter) {
	          return new OperatorNode('*', 'multiply', node.args.map(function (argInner) {
	            return argInner === argOuter ? _derivative(argInner, isConst) : argInner.clone();
	          }));
	        }));
	      }
	      if (node.op === '/' && node.isBinary()) {
	        const arg0 = node.args[0];
	        const arg1 = node.args[1];

	        // d/dx(f(x) / c) = f'(x) / c
	        if (isConst(arg1)) {
	          return new OperatorNode('/', 'divide', [_derivative(arg0, isConst), arg1]);
	        }

	        // Reciprocal Rule, d/dx(c / f(x)) = -c(f'(x)/f(x)^2)
	        if (isConst(arg0)) {
	          return new OperatorNode('*', 'multiply', [new OperatorNode('-', 'unaryMinus', [arg0]), new OperatorNode('/', 'divide', [_derivative(arg1, isConst), new OperatorNode('^', 'pow', [arg1.clone(), createConstantNode(2)])])]);
	        }

	        // Quotient rule, d/dx(f(x) / g(x)) = (f'(x)g(x) - f(x)g'(x)) / g(x)^2
	        return new OperatorNode('/', 'divide', [new OperatorNode('-', 'subtract', [new OperatorNode('*', 'multiply', [_derivative(arg0, isConst), arg1.clone()]), new OperatorNode('*', 'multiply', [arg0.clone(), _derivative(arg1, isConst)])]), new OperatorNode('^', 'pow', [arg1.clone(), createConstantNode(2)])]);
	      }
	      if (node.op === '^' && node.isBinary()) {
	        const arg0 = node.args[0];
	        const arg1 = node.args[1];
	        if (isConst(arg0)) {
	          // If is secretly constant; 0^f(x) = 1 (in JS), 1^f(x) = 1
	          if ((0, _is.isConstantNode)(arg0) && (isZero(arg0.value) || equal(arg0.value, 1))) {
	            return createConstantNode(0);
	          }

	          // d/dx(c^f(x)) = c^f(x)*ln(c)*f'(x)
	          return new OperatorNode('*', 'multiply', [node, new OperatorNode('*', 'multiply', [new FunctionNode('log', [arg0.clone()]), _derivative(arg1.clone(), isConst)])]);
	        }
	        if (isConst(arg1)) {
	          if ((0, _is.isConstantNode)(arg1)) {
	            // If is secretly constant; f(x)^0 = 1 -> d/dx(1) = 0
	            if (isZero(arg1.value)) {
	              return createConstantNode(0);
	            }
	            // Ignore exponent; f(x)^1 = f(x)
	            if (equal(arg1.value, 1)) {
	              return _derivative(arg0, isConst);
	            }
	          }

	          // Elementary Power Rule, d/dx(f(x)^c) = c*f'(x)*f(x)^(c-1)
	          const powMinusOne = new OperatorNode('^', 'pow', [arg0.clone(), new OperatorNode('-', 'subtract', [arg1, createConstantNode(1)])]);
	          return new OperatorNode('*', 'multiply', [arg1.clone(), new OperatorNode('*', 'multiply', [_derivative(arg0, isConst), powMinusOne])]);
	        }

	        // Functional Power Rule, d/dx(f^g) = f^g*[f'*(g/f) + g'ln(f)]
	        return new OperatorNode('*', 'multiply', [new OperatorNode('^', 'pow', [arg0.clone(), arg1.clone()]), new OperatorNode('+', 'add', [new OperatorNode('*', 'multiply', [_derivative(arg0, isConst), new OperatorNode('/', 'divide', [arg1.clone(), arg0.clone()])]), new OperatorNode('*', 'multiply', [_derivative(arg1, isConst), new FunctionNode('log', [arg0.clone()])])])]);
	      }
	      throw new Error('Cannot process operator "' + node.op + '" in derivative: ' + 'the operator is not supported, undefined, or the number of arguments passed to it are not supported');
	    }
	  });

	  /**
	   * Helper function to create a constant node with a specific type
	   * (number, BigNumber, Fraction)
	   * @param {number} value
	   * @param {string} [valueType]
	   * @return {ConstantNode}
	   */
	  function createConstantNode(value, valueType) {
	    return new ConstantNode(numeric(value, valueType || (0, _number.safeNumberType)(String(value), config)));
	  }
	  return derivative;
	});
	return derivative;
}

var rationalize = {};

var hasRequiredRationalize;

function requireRationalize () {
	if (hasRequiredRationalize) return rationalize;
	hasRequiredRationalize = 1;

	Object.defineProperty(rationalize, "__esModule", {
	  value: true
	});
	rationalize.createRationalize = void 0;
	var _number = requireNumber$3();
	var _factory = requireFactory$b();
	const name = 'rationalize';
	const dependencies = ['config', 'typed', 'equal', 'isZero', 'add', 'subtract', 'multiply', 'divide', 'pow', 'parse', 'simplifyConstant', 'simplifyCore', 'simplify', '?bignumber', '?fraction', 'mathWithTransform', 'matrix', 'AccessorNode', 'ArrayNode', 'ConstantNode', 'FunctionNode', 'IndexNode', 'ObjectNode', 'OperatorNode', 'SymbolNode', 'ParenthesisNode'];
	rationalize.createRationalize = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    config,
	    typed,
	    equal,
	    isZero,
	    add,
	    subtract,
	    multiply,
	    divide,
	    pow,
	    parse,
	    simplifyConstant,
	    simplifyCore,
	    simplify,
	    fraction,
	    bignumber,
	    mathWithTransform,
	    matrix,
	    AccessorNode,
	    ArrayNode,
	    ConstantNode,
	    FunctionNode,
	    IndexNode,
	    ObjectNode,
	    OperatorNode,
	    SymbolNode,
	    ParenthesisNode
	  } = _ref;
	  /**
	   * Transform a rationalizable expression in a rational fraction.
	   * If rational fraction is one variable polynomial then converts
	   * the numerator and denominator in canonical form, with decreasing
	   * exponents, returning the coefficients of numerator.
	   *
	   * Syntax:
	   *
	   *     math.rationalize(expr)
	   *     math.rationalize(expr, detailed)
	   *     math.rationalize(expr, scope)
	   *     math.rationalize(expr, scope, detailed)
	   *
	   * Examples:
	   *
	   *     math.rationalize('sin(x)+y')
	   *                   //  Error: There is an unsolved function call
	   *     math.rationalize('2x/y - y/(x+1)')
	   *                   // (2*x^2-y^2+2*x)/(x*y+y)
	   *     math.rationalize('(2x+1)^6')
	   *                   // 64*x^6+192*x^5+240*x^4+160*x^3+60*x^2+12*x+1
	   *     math.rationalize('2x/( (2x-1) / (3x+2) ) - 5x/ ( (3x+4) / (2x^2-5) ) + 3')
	   *                   // -20*x^4+28*x^3+104*x^2+6*x-12)/(6*x^2+5*x-4)
	   *     math.rationalize('x/(1-x)/(x-2)/(x-3)/(x-4) + 2x/ ( (1-2x)/(2-3x) )/ ((3-4x)/(4-5x) )') =
	   *                   // (-30*x^7+344*x^6-1506*x^5+3200*x^4-3472*x^3+1846*x^2-381*x)/
	   *                   //     (-8*x^6+90*x^5-383*x^4+780*x^3-797*x^2+390*x-72)
	   *
	   *     math.rationalize('x+x+x+y',{y:1}) // 3*x+1
	   *     math.rationalize('x+x+x+y',{})    // 3*x+y
	   *
	   *     const ret = math.rationalize('x+x+x+y',{},true)
	   *                   // ret.expression=3*x+y, ret.variables = ["x","y"]
	   *     const ret = math.rationalize('-2+5x^2',{},true)
	   *                   // ret.expression=5*x^2-2, ret.variables = ["x"], ret.coefficients=[-2,0,5]
	   *
	   * See also:
	   *
	   *     simplify
	   *
	   * @param  {Node|string} expr    The expression to check if is a polynomial expression
	   * @param  {Object|boolean}      optional scope of expression or true for already evaluated rational expression at input
	   * @param  {Boolean}  detailed   optional True if return an object, false if return expression node (default)
	   *
	   * @return {Object | Node}    The rational polynomial of `expr` or an object
	   *            `{expression, numerator, denominator, variables, coefficients}`, where
	   *              `expression` is a `Node` with the node simplified expression,
	   *              `numerator` is a `Node` with the simplified numerator of expression,
	   *              `denominator` is a `Node` or `boolean` with the simplified denominator or `false` (if there is no denominator),
	   *              `variables` is an array with variable names,
	   *              and `coefficients` is an array with coefficients of numerator sorted by increased exponent
	   *           {Expression Node}  node simplified expression
	   *
	   */
	  function _rationalize(expr) {
	    let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	    let detailed = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
	    const setRules = rulesRationalize(); // Rules for change polynomial in near canonical form
	    const polyRet = polynomial(expr, scope, true, setRules.firstRules); // Check if expression is a rationalizable polynomial
	    const nVars = polyRet.variables.length;
	    const noExactFractions = {
	      exactFractions: false
	    };
	    const withExactFractions = {
	      exactFractions: true
	    };
	    expr = polyRet.expression;
	    if (nVars >= 1) {
	      // If expression in not a constant
	      expr = expandPower(expr); // First expand power of polynomials (cannot be made from rules!)
	      let sBefore; // Previous expression
	      let rules;
	      let eDistrDiv = true;
	      let redoInic = false;
	      // Apply the initial rules, including succ div rules:
	      expr = simplify(expr, setRules.firstRules, {}, noExactFractions);
	      let s;
	      while (true) {
	        // Alternate applying successive division rules and distr.div.rules
	        // until there are no more changes:
	        rules = eDistrDiv ? setRules.distrDivRules : setRules.sucDivRules;
	        expr = simplify(expr, rules, {}, withExactFractions);
	        eDistrDiv = !eDistrDiv; // Swap between Distr.Div and Succ. Div. Rules

	        s = expr.toString();
	        if (s === sBefore) {
	          break; // No changes : end of the loop
	        }
	        redoInic = true;
	        sBefore = s;
	      }
	      if (redoInic) {
	        // Apply first rules again without succ div rules (if there are changes)
	        expr = simplify(expr, setRules.firstRulesAgain, {}, noExactFractions);
	      }
	      // Apply final rules:
	      expr = simplify(expr, setRules.finalRules, {}, noExactFractions);
	    } // NVars >= 1

	    const coefficients = [];
	    const retRationalize = {};
	    if (expr.type === 'OperatorNode' && expr.isBinary() && expr.op === '/') {
	      // Separate numerator from denominator
	      if (nVars === 1) {
	        expr.args[0] = polyToCanonical(expr.args[0], coefficients);
	        expr.args[1] = polyToCanonical(expr.args[1]);
	      }
	      if (detailed) {
	        retRationalize.numerator = expr.args[0];
	        retRationalize.denominator = expr.args[1];
	      }
	    } else {
	      if (nVars === 1) {
	        expr = polyToCanonical(expr, coefficients);
	      }
	      if (detailed) {
	        retRationalize.numerator = expr;
	        retRationalize.denominator = null;
	      }
	    }
	    // nVars

	    if (!detailed) return expr;
	    retRationalize.coefficients = coefficients;
	    retRationalize.variables = polyRet.variables;
	    retRationalize.expression = expr;
	    return retRationalize;
	  }
	  return typed(name, {
	    Node: _rationalize,
	    'Node, boolean': (expr, detailed) => _rationalize(expr, {}, detailed),
	    'Node, Object': _rationalize,
	    'Node, Object, boolean': _rationalize
	  }); // end of typed rationalize

	  /**
	   *  Function to simplify an expression using an optional scope and
	   *  return it if the expression is a polynomial expression, i.e.
	   *  an expression with one or more variables and the operators
	   *  +, -, *, and ^, where the exponent can only be a positive integer.
	   *
	   * Syntax:
	   *
	   *     polynomial(expr,scope,extended, rules)
	   *
	   * @param  {Node | string} expr     The expression to simplify and check if is polynomial expression
	   * @param  {object} scope           Optional scope for expression simplification
	   * @param  {boolean} extended       Optional. Default is false. When true allows divide operator.
	   * @param  {array}  rules           Optional. Default is no rule.
	   *
	   *
	   * @return {Object}
	   *            {Object} node:   node simplified expression
	   *            {Array}  variables:  variable names
	   */
	  function polynomial(expr, scope, extended, rules) {
	    const variables = [];
	    const node = simplify(expr, rules, scope, {
	      exactFractions: false
	    }); // Resolves any variables and functions with all defined parameters
	    extended = !!extended;
	    const oper = '+-*' + (extended ? '/' : '');
	    recPoly(node);
	    const retFunc = {};
	    retFunc.expression = node;
	    retFunc.variables = variables;
	    return retFunc;

	    // -------------------------------------------------------------------------------------------------------

	    /**
	     *  Function to simplify an expression using an optional scope and
	     *  return it if the expression is a polynomial expression, i.e.
	     *  an expression with one or more variables and the operators
	     *  +, -, *, and ^, where the exponent can only be a positive integer.
	     *
	     * Syntax:
	     *
	     *     recPoly(node)
	     *
	     *
	     * @param  {Node} node               The current sub tree expression in recursion
	     *
	     * @return                           nothing, throw an exception if error
	     */
	    function recPoly(node) {
	      const tp = node.type; // node type
	      if (tp === 'FunctionNode') {
	        // No function call in polynomial expression
	        throw new Error('There is an unsolved function call');
	      } else if (tp === 'OperatorNode') {
	        if (node.op === '^') {
	          // TODO: handle negative exponents like in '1/x^(-2)'
	          if (node.args[1].type !== 'ConstantNode' || !(0, _number.isInteger)(parseFloat(node.args[1].value))) {
	            throw new Error('There is a non-integer exponent');
	          } else {
	            recPoly(node.args[0]);
	          }
	        } else {
	          if (!oper.includes(node.op)) {
	            throw new Error('Operator ' + node.op + ' invalid in polynomial expression');
	          }
	          for (let i = 0; i < node.args.length; i++) {
	            recPoly(node.args[i]);
	          }
	        } // type of operator
	      } else if (tp === 'SymbolNode') {
	        const name = node.name; // variable name
	        const pos = variables.indexOf(name);
	        if (pos === -1) {
	          // new variable in expression
	          variables.push(name);
	        }
	      } else if (tp === 'ParenthesisNode') {
	        recPoly(node.content);
	      } else if (tp !== 'ConstantNode') {
	        throw new Error('type ' + tp + ' is not allowed in polynomial expression');
	      }
	    } // end of recPoly
	  } // end of polynomial

	  // ---------------------------------------------------------------------------------------
	  /**
	   * Return a rule set to rationalize an polynomial expression in rationalize
	   *
	   * Syntax:
	   *
	   *     rulesRationalize()
	   *
	   * @return {array}        rule set to rationalize an polynomial expression
	   */
	  function rulesRationalize() {
	    const oldRules = [simplifyCore,
	    // sCore
	    {
	      l: 'n+n',
	      r: '2*n'
	    }, {
	      l: 'n+-n',
	      r: '0'
	    }, simplifyConstant,
	    // sConstant
	    {
	      l: 'n*(n1^-1)',
	      r: 'n/n1'
	    }, {
	      l: 'n*n1^-n2',
	      r: 'n/n1^n2'
	    }, {
	      l: 'n1^-1',
	      r: '1/n1'
	    }, {
	      l: 'n*(n1/n2)',
	      r: '(n*n1)/n2'
	    }, {
	      l: '1*n',
	      r: 'n'
	    }];
	    const rulesFirst = [{
	      l: '(-n1)/(-n2)',
	      r: 'n1/n2'
	    },
	    // Unary division
	    {
	      l: '(-n1)*(-n2)',
	      r: 'n1*n2'
	    },
	    // Unary multiplication
	    {
	      l: 'n1--n2',
	      r: 'n1+n2'
	    },
	    // '--' elimination
	    {
	      l: 'n1-n2',
	      r: 'n1+(-n2)'
	    },
	    // Subtraction turn into add with un�ry minus
	    {
	      l: '(n1+n2)*n3',
	      r: '(n1*n3 + n2*n3)'
	    },
	    // Distributive 1
	    {
	      l: 'n1*(n2+n3)',
	      r: '(n1*n2+n1*n3)'
	    },
	    // Distributive 2
	    {
	      l: 'c1*n + c2*n',
	      r: '(c1+c2)*n'
	    },
	    // Joining constants
	    {
	      l: 'c1*n + n',
	      r: '(c1+1)*n'
	    },
	    // Joining constants
	    {
	      l: 'c1*n - c2*n',
	      r: '(c1-c2)*n'
	    },
	    // Joining constants
	    {
	      l: 'c1*n - n',
	      r: '(c1-1)*n'
	    },
	    // Joining constants
	    {
	      l: 'v/c',
	      r: '(1/c)*v'
	    },
	    // variable/constant (new!)
	    {
	      l: 'v/-c',
	      r: '-(1/c)*v'
	    },
	    // variable/constant (new!)
	    {
	      l: '-v*-c',
	      r: 'c*v'
	    },
	    // Inversion constant and variable 1
	    {
	      l: '-v*c',
	      r: '-c*v'
	    },
	    // Inversion constant and variable 2
	    {
	      l: 'v*-c',
	      r: '-c*v'
	    },
	    // Inversion constant and variable 3
	    {
	      l: 'v*c',
	      r: 'c*v'
	    },
	    // Inversion constant and variable 4
	    {
	      l: '-(-n1*n2)',
	      r: '(n1*n2)'
	    },
	    // Unary propagation
	    {
	      l: '-(n1*n2)',
	      r: '(-n1*n2)'
	    },
	    // Unary propagation
	    {
	      l: '-(-n1+n2)',
	      r: '(n1-n2)'
	    },
	    // Unary propagation
	    {
	      l: '-(n1+n2)',
	      r: '(-n1-n2)'
	    },
	    // Unary propagation
	    {
	      l: '(n1^n2)^n3',
	      r: '(n1^(n2*n3))'
	    },
	    // Power to Power
	    {
	      l: '-(-n1/n2)',
	      r: '(n1/n2)'
	    },
	    // Division and Unary
	    {
	      l: '-(n1/n2)',
	      r: '(-n1/n2)'
	    }]; // Divisao and Unary

	    const rulesDistrDiv = [{
	      l: '(n1/n2 + n3/n4)',
	      r: '((n1*n4 + n3*n2)/(n2*n4))'
	    },
	    // Sum of fractions
	    {
	      l: '(n1/n2 + n3)',
	      r: '((n1 + n3*n2)/n2)'
	    },
	    // Sum fraction with number 1
	    {
	      l: '(n1 + n2/n3)',
	      r: '((n1*n3 + n2)/n3)'
	    }]; // Sum fraction with number 1

	    const rulesSucDiv = [{
	      l: '(n1/(n2/n3))',
	      r: '((n1*n3)/n2)'
	    },
	    // Division simplification
	    {
	      l: '(n1/n2/n3)',
	      r: '(n1/(n2*n3))'
	    }];
	    const setRules = {}; // rules set in 4 steps.

	    // All rules => infinite loop
	    // setRules.allRules =oldRules.concat(rulesFirst,rulesDistrDiv,rulesSucDiv)

	    setRules.firstRules = oldRules.concat(rulesFirst, rulesSucDiv); // First rule set
	    setRules.distrDivRules = rulesDistrDiv; // Just distr. div. rules
	    setRules.sucDivRules = rulesSucDiv; // Jus succ. div. rules
	    setRules.firstRulesAgain = oldRules.concat(rulesFirst); // Last rules set without succ. div.

	    // Division simplification

	    // Second rule set.
	    // There is no aggregate expression with parentesis, but the only variable can be scattered.
	    setRules.finalRules = [simplifyCore,
	    // simplify.rules[0]
	    {
	      l: 'n*-n',
	      r: '-n^2'
	    },
	    // Joining multiply with power 1
	    {
	      l: 'n*n',
	      r: 'n^2'
	    },
	    // Joining multiply with power 2
	    simplifyConstant,
	    // simplify.rules[14] old 3rd index in oldRules
	    {
	      l: 'n*-n^n1',
	      r: '-n^(n1+1)'
	    },
	    // Joining multiply with power 3
	    {
	      l: 'n*n^n1',
	      r: 'n^(n1+1)'
	    },
	    // Joining multiply with power 4
	    {
	      l: 'n^n1*-n^n2',
	      r: '-n^(n1+n2)'
	    },
	    // Joining multiply with power 5
	    {
	      l: 'n^n1*n^n2',
	      r: 'n^(n1+n2)'
	    },
	    // Joining multiply with power 6
	    {
	      l: 'n^n1*-n',
	      r: '-n^(n1+1)'
	    },
	    // Joining multiply with power 7
	    {
	      l: 'n^n1*n',
	      r: 'n^(n1+1)'
	    },
	    // Joining multiply with power 8
	    {
	      l: 'n^n1/-n',
	      r: '-n^(n1-1)'
	    },
	    // Joining multiply with power 8
	    {
	      l: 'n^n1/n',
	      r: 'n^(n1-1)'
	    },
	    // Joining division with power 1
	    {
	      l: 'n/-n^n1',
	      r: '-n^(1-n1)'
	    },
	    // Joining division with power 2
	    {
	      l: 'n/n^n1',
	      r: 'n^(1-n1)'
	    },
	    // Joining division with power 3
	    {
	      l: 'n^n1/-n^n2',
	      r: 'n^(n1-n2)'
	    },
	    // Joining division with power 4
	    {
	      l: 'n^n1/n^n2',
	      r: 'n^(n1-n2)'
	    },
	    // Joining division with power 5
	    {
	      l: 'n1+(-n2*n3)',
	      r: 'n1-n2*n3'
	    },
	    // Solving useless parenthesis 1
	    {
	      l: 'v*(-c)',
	      r: '-c*v'
	    },
	    // Solving useless unary 2
	    {
	      l: 'n1+-n2',
	      r: 'n1-n2'
	    },
	    // Solving +- together (new!)
	    {
	      l: 'v*c',
	      r: 'c*v'
	    },
	    // inversion constant with variable
	    {
	      l: '(n1^n2)^n3',
	      r: '(n1^(n2*n3))'
	    } // Power to Power
	    ];
	    return setRules;
	  } // End rulesRationalize

	  // ---------------------------------------------------------------------------------------
	  /**
	   *  Expand recursively a tree node for handling with expressions with exponents
	   *  (it's not for constants, symbols or functions with exponents)
	   *  PS: The other parameters are internal for recursion
	   *
	   * Syntax:
	   *
	   *     expandPower(node)
	   *
	   * @param  {Node} node         Current expression node
	   * @param  {node} parent       Parent current node inside the recursion
	   * @param  (int}               Parent number of chid inside the rercursion
	   *
	   * @return {node}        node expression with all powers expanded.
	   */
	  function expandPower(node, parent, indParent) {
	    const tp = node.type;
	    const internal = arguments.length > 1; // TRUE in internal calls

	    if (tp === 'OperatorNode' && node.isBinary()) {
	      let does = false;
	      let val;
	      if (node.op === '^') {
	        // First operator: Parenthesis or UnaryMinus
	        if ((node.args[0].type === 'ParenthesisNode' || node.args[0].type === 'OperatorNode') && node.args[1].type === 'ConstantNode') {
	          // Second operator: Constant
	          val = parseFloat(node.args[1].value);
	          does = val >= 2 && (0, _number.isInteger)(val);
	        }
	      }
	      if (does) {
	        // Exponent >= 2
	        // Before:
	        //            operator A --> Subtree
	        // parent pow
	        //            constant
	        //
	        if (val > 2) {
	          // Exponent > 2,
	          // AFTER:  (exponent > 2)
	          //             operator A --> Subtree
	          // parent  *
	          //                 deep clone (operator A --> Subtree
	          //             pow
	          //                 constant - 1
	          //
	          const nEsqTopo = node.args[0];
	          const nDirTopo = new OperatorNode('^', 'pow', [node.args[0].cloneDeep(), new ConstantNode(val - 1)]);
	          node = new OperatorNode('*', 'multiply', [nEsqTopo, nDirTopo]);
	        } else {
	          // Expo = 2 - no power
	          // AFTER:  (exponent =  2)
	          //             operator A --> Subtree
	          // parent   oper
	          //            deep clone (operator A --> Subtree)
	          //
	          node = new OperatorNode('*', 'multiply', [node.args[0], node.args[0].cloneDeep()]);
	        }
	        if (internal) {
	          // Change parent references in internal recursive calls
	          if (indParent === 'content') {
	            parent.content = node;
	          } else {
	            parent.args[indParent] = node;
	          }
	        }
	      } // does
	    } // binary OperatorNode

	    if (tp === 'ParenthesisNode') {
	      // Recursion
	      expandPower(node.content, node, 'content');
	    } else if (tp !== 'ConstantNode' && tp !== 'SymbolNode') {
	      for (let i = 0; i < node.args.length; i++) {
	        expandPower(node.args[i], node, i);
	      }
	    }
	    if (!internal) {
	      // return the root node
	      return node;
	    }
	  } // End expandPower

	  // ---------------------------------------------------------------------------------------
	  /**
	   * Auxilary function for rationalize
	   * Convert near canonical polynomial in one variable in a canonical polynomial
	   * with one term for each exponent in decreasing order
	   *
	   * Syntax:
	   *
	   *     polyToCanonical(node [, coefficients])
	   *
	   * @param  {Node | string} expr       The near canonical polynomial expression to convert in a a canonical polynomial expression
	   *
	   *        The string or tree expression needs to be at below syntax, with free spaces:
	   *         (  (^(-)? | [+-]? )cte (*)? var (^expo)?  | cte )+
	   *       Where 'var' is one variable with any valid name
	   *             'cte' are real numeric constants with any value. It can be omitted if equal than 1
	   *             'expo' are integers greater than 0. It can be omitted if equal than 1.
	   *
	   * @param  {array}   coefficients             Optional returns coefficients sorted by increased exponent
	   *
	   *
	   * @return {node}        new node tree with one variable polynomial or string error.
	   */
	  function polyToCanonical(node, coefficients) {
	    if (coefficients === undefined) {
	      coefficients = [];
	    } // coefficients.

	    coefficients[0] = 0; // index is the exponent
	    const o = {};
	    o.cte = 1;
	    o.oper = '+';

	    // fire: mark with * or ^ when finds * or ^ down tree, reset to "" with + and -.
	    //       It is used to deduce the exponent: 1 for *, 0 for "".
	    o.fire = '';
	    let maxExpo = 0; // maximum exponent
	    let varname = ''; // variable name

	    recurPol(node, null, o);
	    maxExpo = coefficients.length - 1;
	    let first = true;
	    let no;
	    for (let i = maxExpo; i >= 0; i--) {
	      if (coefficients[i] === 0) continue;
	      let n1 = new ConstantNode(first ? coefficients[i] : Math.abs(coefficients[i]));
	      const op = coefficients[i] < 0 ? '-' : '+';
	      if (i > 0) {
	        // Is not a constant without variable
	        let n2 = new SymbolNode(varname);
	        if (i > 1) {
	          const n3 = new ConstantNode(i);
	          n2 = new OperatorNode('^', 'pow', [n2, n3]);
	        }
	        if (coefficients[i] === -1 && first) {
	          n1 = new OperatorNode('-', 'unaryMinus', [n2]);
	        } else if (Math.abs(coefficients[i]) === 1) {
	          n1 = n2;
	        } else {
	          n1 = new OperatorNode('*', 'multiply', [n1, n2]);
	        }
	      }
	      if (first) {
	        no = n1;
	      } else if (op === '+') {
	        no = new OperatorNode('+', 'add', [no, n1]);
	      } else {
	        no = new OperatorNode('-', 'subtract', [no, n1]);
	      }
	      first = false;
	    } // for

	    if (first) {
	      return new ConstantNode(0);
	    } else {
	      return no;
	    }

	    /**
	     * Recursive auxilary function inside polyToCanonical for
	     * converting expression in canonical form
	     *
	     * Syntax:
	     *
	     *     recurPol(node, noPai, obj)
	     *
	     * @param  {Node} node        The current subpolynomial expression
	     * @param  {Node | Null}  noPai   The current parent node
	     * @param  {object}    obj        Object with many internal flags
	     *
	     * @return {}                    No return. If error, throws an exception
	     */
	    function recurPol(node, noPai, o) {
	      const tp = node.type;
	      if (tp === 'FunctionNode') {
	        // ***** FunctionName *****
	        // No function call in polynomial expression
	        throw new Error('There is an unsolved function call');
	      } else if (tp === 'OperatorNode') {
	        // ***** OperatorName *****
	        if (!'+-*^'.includes(node.op)) throw new Error('Operator ' + node.op + ' invalid');
	        if (noPai !== null) {
	          // -(unary),^  : children of *,+,-
	          if ((node.fn === 'unaryMinus' || node.fn === 'pow') && noPai.fn !== 'add' && noPai.fn !== 'subtract' && noPai.fn !== 'multiply') {
	            throw new Error('Invalid ' + node.op + ' placing');
	          }

	          // -,+,* : children of +,-
	          if ((node.fn === 'subtract' || node.fn === 'add' || node.fn === 'multiply') && noPai.fn !== 'add' && noPai.fn !== 'subtract') {
	            throw new Error('Invalid ' + node.op + ' placing');
	          }

	          // -,+ : first child
	          if ((node.fn === 'subtract' || node.fn === 'add' || node.fn === 'unaryMinus') && o.noFil !== 0) {
	            throw new Error('Invalid ' + node.op + ' placing');
	          }
	        } // Has parent

	        // Firers: ^,*       Old:   ^,&,-(unary): firers
	        if (node.op === '^' || node.op === '*') {
	          o.fire = node.op;
	        }
	        for (let i = 0; i < node.args.length; i++) {
	          // +,-: reset fire
	          if (node.fn === 'unaryMinus') o.oper = '-';
	          if (node.op === '+' || node.fn === 'subtract') {
	            o.fire = '';
	            o.cte = 1; // default if there is no constant
	            o.oper = i === 0 ? '+' : node.op;
	          }
	          o.noFil = i; // number of son
	          recurPol(node.args[i], node, o);
	        } // for in children
	      } else if (tp === 'SymbolNode') {
	        // ***** SymbolName *****
	        if (node.name !== varname && varname !== '') {
	          throw new Error('There is more than one variable');
	        }
	        varname = node.name;
	        if (noPai === null) {
	          coefficients[1] = 1;
	          return;
	        }

	        // ^: Symbol is First child
	        if (noPai.op === '^' && o.noFil !== 0) {
	          throw new Error('In power the variable should be the first parameter');
	        }

	        // *: Symbol is Second child
	        if (noPai.op === '*' && o.noFil !== 1) {
	          throw new Error('In multiply the variable should be the second parameter');
	        }

	        // Symbol: firers '',* => it means there is no exponent above, so it's 1 (cte * var)
	        if (o.fire === '' || o.fire === '*') {
	          if (maxExpo < 1) coefficients[1] = 0;
	          coefficients[1] += o.cte * (o.oper === '+' ? 1 : -1);
	          maxExpo = Math.max(1, maxExpo);
	        }
	      } else if (tp === 'ConstantNode') {
	        const valor = parseFloat(node.value);
	        if (noPai === null) {
	          coefficients[0] = valor;
	          return;
	        }
	        if (noPai.op === '^') {
	          // cte: second  child of power
	          if (o.noFil !== 1) throw new Error('Constant cannot be powered');
	          if (!(0, _number.isInteger)(valor) || valor <= 0) {
	            throw new Error('Non-integer exponent is not allowed');
	          }
	          for (let i = maxExpo + 1; i < valor; i++) coefficients[i] = 0;
	          if (valor > maxExpo) coefficients[valor] = 0;
	          coefficients[valor] += o.cte * (o.oper === '+' ? 1 : -1);
	          maxExpo = Math.max(valor, maxExpo);
	          return;
	        }
	        o.cte = valor;

	        // Cte: firer '' => There is no exponent and no multiplication, so the exponent is 0.
	        if (o.fire === '') {
	          coefficients[0] += o.cte * (o.oper === '+' ? 1 : -1);
	        }
	      } else {
	        throw new Error('Type ' + tp + ' is not allowed');
	      }
	    } // End of recurPol
	  } // End of polyToCanonical
	});
	return rationalize;
}

var zpk2tf = {};

var hasRequiredZpk2tf;

function requireZpk2tf () {
	if (hasRequiredZpk2tf) return zpk2tf;
	hasRequiredZpk2tf = 1;

	Object.defineProperty(zpk2tf, "__esModule", {
	  value: true
	});
	zpk2tf.createZpk2tf = void 0;
	var _factory = requireFactory$b();
	const name = 'zpk2tf';
	const dependencies = ['typed', 'add', 'multiply', 'Complex', 'number'];
	zpk2tf.createZpk2tf = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    multiply,
	    Complex,
	    number
	  } = _ref;
	  /**
	     * Compute the transfer function of a zero-pole-gain model.
	     *
	     * Syntax:
	     *      math.zpk2tf(z, p, k)
	     *
	     * Examples:
	     *    math.zpk2tf([1, 2], [-1, -2], 1)    // returns [[1, -3, 2], [1, 3, 2]]
	     *
	     * See also:
	     *   freqz
	     *
	     * @param {Array} z Array of zeros values
	     * @param {Array} p Array of poles values
	     * @param {number} k Gain value
	     * @return {Array} Two dimensional array containing the numerator (first row) and denominator (second row) polynomials
	     *
	     */
	  return typed(name, {
	    'Array,Array,number': function (z, p, k) {
	      return _zpk2tf(z, p, k);
	    },
	    'Array,Array': function (z, p) {
	      return _zpk2tf(z, p, 1);
	    },
	    'Matrix,Matrix,number': function (z, p, k) {
	      return _zpk2tf(z.valueOf(), p.valueOf(), k);
	    },
	    'Matrix,Matrix': function (z, p) {
	      return _zpk2tf(z.valueOf(), p.valueOf(), 1);
	    }
	  });
	  function _zpk2tf(z, p, k) {
	    // if z is bignumber, convert it to number
	    if (z.some(el => el.type === 'BigNumber')) {
	      z = z.map(el => number(el));
	    }
	    // if p is bignumber, convert it to number
	    if (p.some(el => el.type === 'BigNumber')) {
	      p = p.map(el => number(el));
	    }
	    let num = [Complex(1, 0)];
	    let den = [Complex(1, 0)];
	    for (let i = 0; i < z.length; i++) {
	      let zero = z[i];
	      if (typeof zero === 'number') zero = Complex(zero, 0);
	      num = _multiply(num, [Complex(1, 0), Complex(-zero.re, -zero.im)]);
	    }
	    for (let i = 0; i < p.length; i++) {
	      let pole = p[i];
	      if (typeof pole === 'number') pole = Complex(pole, 0);
	      den = _multiply(den, [Complex(1, 0), Complex(-pole.re, -pole.im)]);
	    }
	    for (let i = 0; i < num.length; i++) {
	      num[i] = multiply(num[i], k);
	    }
	    return [num, den];
	  }
	  function _multiply(a, b) {
	    const c = [];
	    for (let i = 0; i < a.length + b.length - 1; i++) {
	      c[i] = Complex(0, 0);
	      for (let j = 0; j < a.length; j++) {
	        if (i - j >= 0 && i - j < b.length) {
	          c[i] = add(c[i], multiply(a[j], b[i - j]));
	        }
	      }
	    }
	    return c;
	  }
	});
	return zpk2tf;
}

var freqz = {};

var hasRequiredFreqz;

function requireFreqz () {
	if (hasRequiredFreqz) return freqz;
	hasRequiredFreqz = 1;

	Object.defineProperty(freqz, "__esModule", {
	  value: true
	});
	freqz.createFreqz = void 0;
	var _factory = requireFactory$b();
	const name = 'freqz';
	const dependencies = ['typed', 'add', 'multiply', 'Complex', 'divide', 'matrix'];
	freqz.createFreqz = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    multiply,
	    Complex,
	    divide,
	    matrix
	  } = _ref;
	  /**
	     * Calculates the frequency response of a filter given its numerator and denominator coefficients.
	     *
	     * Syntax:
	     *    math.freqz(b, a)
	     *    math.freqz(b, a, w)
	     *
	     * Examples:
	     *   math.freqz([1, 2], [1, 2, 3], 4) // returns { h: [0.5 + 0i, 0.4768589245763655 + 0.2861153547458193i, 0.25000000000000006 + 0.75i, -0.770976571635189 + 0.4625859429811135i], w: [0, 0.7853981633974483, 1.5707963267948966, 2.356194490192345 ] }
	     *   math.freqz([1, 2], [1, 2, 3], [0, 1]) // returns { h: [0.5 + 0i, 0.45436781 + 0.38598051i], w: [0, 1] }
	     *
	     * See also:
	     *  zpk2tf
	     *
	     * @param {Array.<number>} b The numerator coefficients of the filter.
	     * @param {Array.<number>} a The denominator coefficients of the filter.
	     * @param {Array.<number>} [w] A vector of frequencies (in radians/sample) at which the frequency response is to be computed or the number of points to compute (if a number is not provided, the default is 512 points)
	     * @returns {Object} An object with two properties: h, a vector containing the complex frequency response, and w, a vector containing the normalized frequencies (in radians/sample) at which the response was computed.
	     *
	     *
	     */
	  return typed(name, {
	    'Array, Array': function (b, a) {
	      const w = createBins(512);
	      return _freqz(b, a, w);
	    },
	    'Array, Array, Array': function (b, a, w) {
	      return _freqz(b, a, w);
	    },
	    'Array, Array, number': function (b, a, w) {
	      if (w < 0) {
	        throw new Error('w must be a positive number');
	      }
	      const w2 = createBins(w);
	      return _freqz(b, a, w2);
	    },
	    'Matrix, Matrix': function (b, a) {
	      // console.log('here')
	      const _w = createBins(512);
	      const {
	        w,
	        h
	      } = _freqz(b.valueOf(), a.valueOf(), _w);
	      return {
	        w: matrix(w),
	        h: matrix(h)
	      };
	    },
	    'Matrix, Matrix, Matrix': function (b, a, w) {
	      const {
	        h
	      } = _freqz(b.valueOf(), a.valueOf(), w.valueOf());
	      return {
	        h: matrix(h),
	        w: matrix(w)
	      };
	    },
	    'Matrix, Matrix, number': function (b, a, w) {
	      if (w < 0) {
	        throw new Error('w must be a positive number');
	      }
	      const _w = createBins(w);
	      const {
	        h
	      } = _freqz(b.valueOf(), a.valueOf(), _w);
	      return {
	        h: matrix(h),
	        w: matrix(_w)
	      };
	    }
	  });
	  function _freqz(b, a, w) {
	    const num = [];
	    const den = [];
	    for (let i = 0; i < w.length; i++) {
	      let sumNum = Complex(0, 0);
	      let sumDen = Complex(0, 0);
	      for (let j = 0; j < b.length; j++) {
	        sumNum = add(sumNum, multiply(b[j], Complex(Math.cos(-j * w[i]), Math.sin(-j * w[i]))));
	      }
	      for (let j = 0; j < a.length; j++) {
	        sumDen = add(sumDen, multiply(a[j], Complex(Math.cos(-j * w[i]), Math.sin(-j * w[i]))));
	      }
	      num.push(sumNum);
	      den.push(sumDen);
	    }
	    const h = [];
	    for (let i = 0; i < num.length; i++) {
	      h.push(divide(num[i], den[i]));
	    }
	    return {
	      h,
	      w
	    };
	  }
	  function createBins(n) {
	    const bins = [];
	    for (let i = 0; i < n; i++) {
	      bins.push(i / n * Math.PI);
	    }
	    return bins;
	  }
	});
	return freqz;
}

var reviver = {};

var hasRequiredReviver;

function requireReviver () {
	if (hasRequiredReviver) return reviver;
	hasRequiredReviver = 1;

	Object.defineProperty(reviver, "__esModule", {
	  value: true
	});
	reviver.createReviver = void 0;
	var _factory = requireFactory$b();
	const name = 'reviver';
	const dependencies = ['classes'];
	reviver.createReviver = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    classes
	  } = _ref;
	  /**
	   * Instantiate mathjs data types from their JSON representation
	   * @param {string} key
	   * @param {*} value
	   * @returns {*} Returns the revived object
	   */
	  return function reviver(key, value) {
	    const constructor = classes[value && value.mathjs];
	    if (constructor && typeof constructor.fromJSON === 'function') {
	      return constructor.fromJSON(value);
	    }
	    return value;
	  };
	});
	return reviver;
}

var replacer = {};

var hasRequiredReplacer;

function requireReplacer () {
	if (hasRequiredReplacer) return replacer;
	hasRequiredReplacer = 1;

	Object.defineProperty(replacer, "__esModule", {
	  value: true
	});
	replacer.createReplacer = void 0;
	var _factory = requireFactory$b();
	const name = 'replacer';
	const dependencies = [];
	replacer.createReplacer = /* #__PURE__ */(0, _factory.factory)(name, dependencies, () => {
	  /**
	   * Stringify data types into their JSON representation.
	   * Most data types can be serialized using their `.toJSON` method,
	   * but not all, for example the number `Infinity`. For these cases you have
	   * to use the replacer. Example usage:
	   *
	   *     JSON.stringify([2, Infinity], math.replacer)
	   *
	   * @param {string} key
	   * @param {*} value
	   * @returns {*} Returns the replaced object
	   */
	  return function replacer(key, value) {
	    // the numeric values Infinitiy, -Infinity, and NaN cannot be serialized to JSON
	    if (typeof value === 'number' && (!Number.isFinite(value) || isNaN(value))) {
	      return {
	        mathjs: 'number',
	        value: String(value)
	      };
	    }
	    if (typeof value === 'bigint') {
	      return {
	        mathjs: 'bigint',
	        value: String(value)
	      };
	    }
	    return value;
	  };
	});
	return replacer;
}

var constants$2 = {};

var version$1 = {};

var hasRequiredVersion;

function requireVersion () {
	if (hasRequiredVersion) return version$1;
	hasRequiredVersion = 1;

	Object.defineProperty(version$1, "__esModule", {
	  value: true
	});
	version$1.version = void 0;
	version$1.version = '15.2.0';
	// Note: This file is automatically generated when building math.js.
	// Changes made in this file will be overwritten.
	return version$1;
}

var hasRequiredConstants$2;

function requireConstants$2 () {
	if (hasRequiredConstants$2) return constants$2;
	hasRequiredConstants$2 = 1;

	Object.defineProperty(constants$2, "__esModule", {
	  value: true
	});
	constants$2.createVersion = constants$2.createUppercasePi = constants$2.createUppercaseE = constants$2.createTrue = constants$2.createTau = constants$2.createSQRT2 = constants$2.createSQRT1_2 = constants$2.createPi = constants$2.createPhi = constants$2.createNull = constants$2.createNaN = constants$2.createLOG2E = constants$2.createLOG10E = constants$2.createLN2 = constants$2.createLN10 = constants$2.createInfinity = constants$2.createI = constants$2.createFalse = constants$2.createE = void 0;
	var _factory = requireFactory$b();
	var _version = requireVersion();
	var _constants = requireConstants$3();
	var _index = requireNumber$2();
	constants$2.createTrue = /* #__PURE__ */(0, _factory.factory)('true', [], () => true);
	constants$2.createFalse = /* #__PURE__ */(0, _factory.factory)('false', [], () => false);
	constants$2.createNull = /* #__PURE__ */(0, _factory.factory)('null', [], () => null);
	constants$2.createInfinity = /* #__PURE__ */recreateFactory('Infinity', ['config', '?BigNumber'], _ref => {
	  let {
	    config,
	    BigNumber
	  } = _ref;
	  return config.number === 'BigNumber' ? new BigNumber(Infinity) : Infinity;
	});
	constants$2.createNaN = /* #__PURE__ */recreateFactory('NaN', ['config', '?BigNumber'], _ref2 => {
	  let {
	    config,
	    BigNumber
	  } = _ref2;
	  return config.number === 'BigNumber' ? new BigNumber(NaN) : NaN;
	});
	constants$2.createPi = /* #__PURE__ */recreateFactory('pi', ['config', '?BigNumber'], _ref3 => {
	  let {
	    config,
	    BigNumber
	  } = _ref3;
	  return config.number === 'BigNumber' ? (0, _constants.createBigNumberPi)(BigNumber) : _index.pi;
	});
	constants$2.createTau = /* #__PURE__ */recreateFactory('tau', ['config', '?BigNumber'], _ref4 => {
	  let {
	    config,
	    BigNumber
	  } = _ref4;
	  return config.number === 'BigNumber' ? (0, _constants.createBigNumberTau)(BigNumber) : _index.tau;
	});
	constants$2.createE = /* #__PURE__ */recreateFactory('e', ['config', '?BigNumber'], _ref5 => {
	  let {
	    config,
	    BigNumber
	  } = _ref5;
	  return config.number === 'BigNumber' ? (0, _constants.createBigNumberE)(BigNumber) : _index.e;
	});

	// golden ratio, (1+sqrt(5))/2
	constants$2.createPhi = /* #__PURE__ */recreateFactory('phi', ['config', '?BigNumber'], _ref6 => {
	  let {
	    config,
	    BigNumber
	  } = _ref6;
	  return config.number === 'BigNumber' ? (0, _constants.createBigNumberPhi)(BigNumber) : _index.phi;
	});
	constants$2.createLN2 = /* #__PURE__ */recreateFactory('LN2', ['config', '?BigNumber'], _ref7 => {
	  let {
	    config,
	    BigNumber
	  } = _ref7;
	  return config.number === 'BigNumber' ? new BigNumber(2).ln() : Math.LN2;
	});
	constants$2.createLN10 = /* #__PURE__ */recreateFactory('LN10', ['config', '?BigNumber'], _ref8 => {
	  let {
	    config,
	    BigNumber
	  } = _ref8;
	  return config.number === 'BigNumber' ? new BigNumber(10).ln() : Math.LN10;
	});
	constants$2.createLOG2E = /* #__PURE__ */recreateFactory('LOG2E', ['config', '?BigNumber'], _ref9 => {
	  let {
	    config,
	    BigNumber
	  } = _ref9;
	  return config.number === 'BigNumber' ? new BigNumber(1).div(new BigNumber(2).ln()) : Math.LOG2E;
	});
	constants$2.createLOG10E = /* #__PURE__ */recreateFactory('LOG10E', ['config', '?BigNumber'], _ref0 => {
	  let {
	    config,
	    BigNumber
	  } = _ref0;
	  return config.number === 'BigNumber' ? new BigNumber(1).div(new BigNumber(10).ln()) : Math.LOG10E;
	});
	constants$2.createSQRT1_2 = /* #__PURE__ */recreateFactory(
	// eslint-disable-line camelcase
	'SQRT1_2', ['config', '?BigNumber'], _ref1 => {
	  let {
	    config,
	    BigNumber
	  } = _ref1;
	  return config.number === 'BigNumber' ? new BigNumber('0.5').sqrt() : Math.SQRT1_2;
	});
	constants$2.createSQRT2 = /* #__PURE__ */recreateFactory('SQRT2', ['config', '?BigNumber'], _ref10 => {
	  let {
	    config,
	    BigNumber
	  } = _ref10;
	  return config.number === 'BigNumber' ? new BigNumber(2).sqrt() : Math.SQRT2;
	});
	constants$2.createI = /* #__PURE__ */recreateFactory('i', ['Complex'], _ref11 => {
	  let {
	    Complex
	  } = _ref11;
	  return Complex.I;
	});

	// for backward compatibility with v5
	constants$2.createUppercasePi = /* #__PURE__ */(0, _factory.factory)('PI', ['pi'], _ref12 => {
	  let {
	    pi
	  } = _ref12;
	  return pi;
	});
	constants$2.createUppercaseE = /* #__PURE__ */(0, _factory.factory)('E', ['e'], _ref13 => {
	  let {
	    e
	  } = _ref13;
	  return e;
	});
	constants$2.createVersion = /* #__PURE__ */(0, _factory.factory)('version', [], () => _version.version);

	// helper function to create a factory with a flag recreateOnConfigChange
	// idea: allow passing optional properties to be attached to the factory function as 4th argument?
	function recreateFactory(name, dependencies, create) {
	  return (0, _factory.factory)(name, dependencies, create, {
	    recreateOnConfigChange: true
	  });
	}
	return constants$2;
}

var physicalConstants = {};

var hasRequiredPhysicalConstants;

function requirePhysicalConstants () {
	if (hasRequiredPhysicalConstants) return physicalConstants;
	hasRequiredPhysicalConstants = 1;

	Object.defineProperty(physicalConstants, "__esModule", {
	  value: true
	});
	physicalConstants.createWienDisplacement = physicalConstants.createWeakMixingAngle = physicalConstants.createVacuumImpedance = physicalConstants.createThomsonCrossSection = physicalConstants.createStefanBoltzmann = physicalConstants.createSpeedOfLight = physicalConstants.createSecondRadiation = physicalConstants.createSackurTetrode = physicalConstants.createRydberg = physicalConstants.createReducedPlanckConstant = physicalConstants.createQuantumOfCirculation = physicalConstants.createProtonMass = physicalConstants.createPlanckTime = physicalConstants.createPlanckTemperature = physicalConstants.createPlanckMass = physicalConstants.createPlanckLength = physicalConstants.createPlanckConstant = physicalConstants.createPlanckCharge = physicalConstants.createNuclearMagneton = physicalConstants.createNeutronMass = physicalConstants.createMolarVolume = physicalConstants.createMolarPlanckConstant = physicalConstants.createMolarMassC12 = physicalConstants.createMolarMass = physicalConstants.createMagneticFluxQuantum = physicalConstants.createMagneticConstant = physicalConstants.createLoschmidt = physicalConstants.createKlitzing = physicalConstants.createJosephson = physicalConstants.createInverseConductanceQuantum = physicalConstants.createHartreeEnergy = physicalConstants.createGravity = physicalConstants.createGravitationConstant = physicalConstants.createGasConstant = physicalConstants.createFirstRadiation = physicalConstants.createFineStructure = physicalConstants.createFermiCoupling = physicalConstants.createFaraday = physicalConstants.createElementaryCharge = physicalConstants.createElectronMass = physicalConstants.createElectricConstant = physicalConstants.createEfimovFactor = physicalConstants.createDeuteronMass = physicalConstants.createCoulombConstant = physicalConstants.createCoulomb = physicalConstants.createConductanceQuantum = physicalConstants.createClassicalElectronRadius = physicalConstants.createBoltzmann = physicalConstants.createBohrRadius = physicalConstants.createBohrMagneton = physicalConstants.createAvogadro = physicalConstants.createAtomicMass = void 0;
	var _factory = requireFactory$b();
	// Source: https://en.wikipedia.org/wiki/Physical_constant

	// Universal constants
	physicalConstants.createSpeedOfLight = /* #__PURE__ */unitFactory('speedOfLight', '299792458', 'm s^-1');
	physicalConstants.createGravitationConstant = /* #__PURE__ */unitFactory('gravitationConstant', '6.67430e-11', 'm^3 kg^-1 s^-2');
	physicalConstants.createPlanckConstant = /* #__PURE__ */unitFactory('planckConstant', '6.62607015e-34', 'J s');
	physicalConstants.createReducedPlanckConstant = /* #__PURE__ */unitFactory('reducedPlanckConstant', '1.0545718176461565e-34', 'J s');

	// Electromagnetic constants
	physicalConstants.createMagneticConstant = /* #__PURE__ */unitFactory('magneticConstant', '1.25663706212e-6', 'N A^-2');
	physicalConstants.createElectricConstant = /* #__PURE__ */unitFactory('electricConstant', '8.8541878128e-12', 'F m^-1');
	physicalConstants.createVacuumImpedance = /* #__PURE__ */unitFactory('vacuumImpedance', '376.730313667', 'ohm');
	physicalConstants.createCoulomb = /* #__PURE__ */unitFactory('coulomb', '8.987551792261171e9', 'N m^2 C^-2'); // Deprecated in favor of coulombConstant
	physicalConstants.createCoulombConstant = /* #__PURE__ */unitFactory('coulombConstant', '8.987551792261171e9', 'N m^2 C^-2');
	physicalConstants.createElementaryCharge = /* #__PURE__ */unitFactory('elementaryCharge', '1.602176634e-19', 'C');
	physicalConstants.createBohrMagneton = /* #__PURE__ */unitFactory('bohrMagneton', '9.2740100783e-24', 'J T^-1');
	physicalConstants.createConductanceQuantum = /* #__PURE__ */unitFactory('conductanceQuantum', '7.748091729863649e-5', 'S');
	physicalConstants.createInverseConductanceQuantum = /* #__PURE__ */unitFactory('inverseConductanceQuantum', '12906.403729652257', 'ohm');
	physicalConstants.createMagneticFluxQuantum = /* #__PURE__ */unitFactory('magneticFluxQuantum', '2.0678338484619295e-15', 'Wb');
	physicalConstants.createNuclearMagneton = /* #__PURE__ */unitFactory('nuclearMagneton', '5.0507837461e-27', 'J T^-1');
	physicalConstants.createKlitzing = /* #__PURE__ */unitFactory('klitzing', '25812.807459304513', 'ohm');
	physicalConstants.createJosephson = /* #__PURE__ */unitFactory('josephson', '4.835978484169836e14 Hz V', 'Hz V^-1'); // TODO: support for Hz needed

	// Atomic and nuclear constants
	physicalConstants.createBohrRadius = /* #__PURE__ */unitFactory('bohrRadius', '5.29177210903e-11', 'm');
	physicalConstants.createClassicalElectronRadius = /* #__PURE__ */unitFactory('classicalElectronRadius', '2.8179403262e-15', 'm');
	physicalConstants.createElectronMass = /* #__PURE__ */unitFactory('electronMass', '9.1093837015e-31', 'kg');
	physicalConstants.createFermiCoupling = /* #__PURE__ */unitFactory('fermiCoupling', '1.1663787e-5', 'GeV^-2');
	physicalConstants.createFineStructure = numberFactory('fineStructure', 7.2973525693e-3);
	physicalConstants.createHartreeEnergy = /* #__PURE__ */unitFactory('hartreeEnergy', '4.3597447222071e-18', 'J');
	physicalConstants.createProtonMass = /* #__PURE__ */unitFactory('protonMass', '1.67262192369e-27', 'kg');
	physicalConstants.createDeuteronMass = /* #__PURE__ */unitFactory('deuteronMass', '3.3435830926e-27', 'kg');
	physicalConstants.createNeutronMass = /* #__PURE__ */unitFactory('neutronMass', '1.6749271613e-27', 'kg');
	physicalConstants.createQuantumOfCirculation = /* #__PURE__ */unitFactory('quantumOfCirculation', '3.6369475516e-4', 'm^2 s^-1');
	physicalConstants.createRydberg = /* #__PURE__ */unitFactory('rydberg', '10973731.568160', 'm^-1');
	physicalConstants.createThomsonCrossSection = /* #__PURE__ */unitFactory('thomsonCrossSection', '6.6524587321e-29', 'm^2');
	physicalConstants.createWeakMixingAngle = numberFactory('weakMixingAngle', 0.22290);
	physicalConstants.createEfimovFactor = numberFactory('efimovFactor', 22.7);

	// Physico-chemical constants
	physicalConstants.createAtomicMass = /* #__PURE__ */unitFactory('atomicMass', '1.66053906660e-27', 'kg');
	physicalConstants.createAvogadro = /* #__PURE__ */unitFactory('avogadro', '6.02214076e23', 'mol^-1');
	physicalConstants.createBoltzmann = /* #__PURE__ */unitFactory('boltzmann', '1.380649e-23', 'J K^-1');
	physicalConstants.createFaraday = /* #__PURE__ */unitFactory('faraday', '96485.33212331001', 'C mol^-1');
	physicalConstants.createFirstRadiation = /* #__PURE__ */unitFactory('firstRadiation', '3.7417718521927573e-16', 'W m^2');
	// TODO spectralRadiance = 1.1910429723971881e-16 W m^2 sr^-1
	physicalConstants.createLoschmidt = /* #__PURE__ */unitFactory('loschmidt', '2.686780111798444e25', 'm^-3');
	physicalConstants.createGasConstant = /* #__PURE__ */unitFactory('gasConstant', '8.31446261815324', 'J K^-1 mol^-1');
	physicalConstants.createMolarPlanckConstant = /* #__PURE__ */unitFactory('molarPlanckConstant', '3.990312712893431e-10', 'J s mol^-1');
	physicalConstants.createMolarVolume = /* #__PURE__ */unitFactory('molarVolume', '0.022413969545014137', 'm^3 mol^-1');
	physicalConstants.createSackurTetrode = numberFactory('sackurTetrode', -1.16487052358);
	physicalConstants.createSecondRadiation = /* #__PURE__ */unitFactory('secondRadiation', '0.014387768775039337', 'm K');
	physicalConstants.createStefanBoltzmann = /* #__PURE__ */unitFactory('stefanBoltzmann', '5.67037441918443e-8', 'W m^-2 K^-4');
	physicalConstants.createWienDisplacement = /* #__PURE__ */unitFactory('wienDisplacement', '2.897771955e-3', 'm K');

	// Adopted values
	physicalConstants.createMolarMass = /* #__PURE__ */unitFactory('molarMass', '0.99999999965e-3', 'kg mol^-1');
	physicalConstants.createMolarMassC12 = /* #__PURE__ */unitFactory('molarMassC12', '11.9999999958e-3', 'kg mol^-1');
	physicalConstants.createGravity = /* #__PURE__ */unitFactory('gravity', '9.80665', 'm s^-2');
	// atm is defined in Unit.js

	// Natural units
	physicalConstants.createPlanckLength = /* #__PURE__ */unitFactory('planckLength', '1.616255e-35', 'm');
	physicalConstants.createPlanckMass = /* #__PURE__ */unitFactory('planckMass', '2.176435e-8', 'kg');
	physicalConstants.createPlanckTime = /* #__PURE__ */unitFactory('planckTime', '5.391245e-44', 's');
	physicalConstants.createPlanckCharge = /* #__PURE__ */unitFactory('planckCharge', '1.87554603778e-18', 'C');
	physicalConstants.createPlanckTemperature = /* #__PURE__ */unitFactory('planckTemperature', '1.416785e+32', 'K');

	// helper function to create a factory function which creates a physical constant,
	// a Unit with either a number value or a BigNumber value depending on the configuration
	function unitFactory(name, valueStr, unitStr) {
	  const dependencies = ['config', 'Unit', 'BigNumber'];
	  return (0, _factory.factory)(name, dependencies, _ref => {
	    let {
	      config,
	      Unit,
	      BigNumber
	    } = _ref;
	    // Note that we can parse into number or BigNumber.
	    // We do not parse into Fractions as that doesn't make sense: we would lose precision of the values
	    // Therefore we dont use Unit.parse()
	    const value = config.number === 'BigNumber' ? new BigNumber(valueStr) : parseFloat(valueStr);
	    const unit = new Unit(value, unitStr);
	    unit.fixPrefix = true;
	    return unit;
	  });
	}

	// helper function to create a factory function which creates a numeric constant,
	// either a number or BigNumber depending on the configuration
	function numberFactory(name, value) {
	  const dependencies = ['config', 'BigNumber'];
	  return (0, _factory.factory)(name, dependencies, _ref2 => {
	    let {
	      config,
	      BigNumber
	    } = _ref2;
	    return config.number === 'BigNumber' ? new BigNumber(value) : value;
	  });
	}
	return physicalConstants;
}

var mapSlices_transform = {};

var hasRequiredMapSlices_transform;

function requireMapSlices_transform () {
	if (hasRequiredMapSlices_transform) return mapSlices_transform;
	hasRequiredMapSlices_transform = 1;

	Object.defineProperty(mapSlices_transform, "__esModule", {
	  value: true
	});
	mapSlices_transform.createMapSlicesTransform = void 0;
	var _errorTransform = requireErrorTransform();
	var _factory = requireFactory$b();
	var _mapSlices = requireMapSlices$1();
	var _is = requireIs();
	const name = 'mapSlices';
	const dependencies = ['typed', 'isInteger'];

	/**
	 * Attach a transform function to math.mapSlices
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the last `dim` parameter of function mapSlices
	 * from one-based to zero based
	 */
	mapSlices_transform.createMapSlicesTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    isInteger
	  } = _ref;
	  const mapSlices = (0, _mapSlices.createMapSlices)({
	    typed,
	    isInteger
	  });

	  // @see: comment of concat itself
	  return typed('mapSlices', {
	    '...any': function (args) {
	      // change dim from one-based to zero-based
	      const dim = args[1];
	      if ((0, _is.isNumber)(dim)) {
	        args[1] = dim - 1;
	      } else if ((0, _is.isBigNumber)(dim)) {
	        args[1] = dim.minus(1);
	      }
	      try {
	        return mapSlices.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true,
	  ..._mapSlices.createMapSlices.meta
	});
	return mapSlices_transform;
}

var column_transform = {};

var hasRequiredColumn_transform;

function requireColumn_transform () {
	if (hasRequiredColumn_transform) return column_transform;
	hasRequiredColumn_transform = 1;

	Object.defineProperty(column_transform, "__esModule", {
	  value: true
	});
	column_transform.createColumnTransform = void 0;
	var _errorTransform = requireErrorTransform();
	var _factory = requireFactory$b();
	var _column = requireColumn$1();
	var _is = requireIs();
	const name = 'column';
	const dependencies = ['typed', 'Index', 'matrix', 'range'];

	/**
	 * Attach a transform function to matrix.column
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the last `index` parameter of function column
	 * from zero-based to one-based
	 */
	column_transform.createColumnTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Index,
	    matrix,
	    range
	  } = _ref;
	  const column = (0, _column.createColumn)({
	    typed,
	    Index,
	    matrix,
	    range
	  });

	  // @see: comment of column itself
	  return typed('column', {
	    '...any': function (args) {
	      // change last argument from zero-based to one-based
	      const lastIndex = args.length - 1;
	      const last = args[lastIndex];
	      if ((0, _is.isNumber)(last)) {
	        args[lastIndex] = last - 1;
	      }
	      try {
	        return column.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return column_transform;
}

var filter_transform = {};

var compileInlineExpression = {};

var hasRequiredCompileInlineExpression;

function requireCompileInlineExpression () {
	if (hasRequiredCompileInlineExpression) return compileInlineExpression;
	hasRequiredCompileInlineExpression = 1;

	Object.defineProperty(compileInlineExpression, "__esModule", {
	  value: true
	});
	compileInlineExpression.compileInlineExpression = compileInlineExpression$1;
	var _is = requireIs();
	var _map = requireMap$2();
	/**
	 * Compile an inline expression like "x > 0"
	 * @param {Node} expression
	 * @param {Object} math
	 * @param {Map} scope
	 * @return {function} Returns a function with one argument which fills in the
	 *                    undefined variable (like "x") and evaluates the expression
	 */
	function compileInlineExpression$1(expression, math, scope) {
	  // find an undefined symbol
	  const symbol = expression.filter(function (node) {
	    return (0, _is.isSymbolNode)(node) && !(node.name in math) && !scope.has(node.name);
	  })[0];
	  if (!symbol) {
	    throw new Error('No undefined variable found in inline expression "' + expression + '"');
	  }

	  // create a test function for this equation
	  const name = symbol.name; // variable name
	  const argsScope = new Map();
	  const subScope = new _map.PartitionedMap(scope, argsScope, new Set([name]));
	  const eq = expression.compile();
	  return function inlineExpression(x) {
	    argsScope.set(name, x);
	    return eq.evaluate(subScope);
	  };
	}
	return compileInlineExpression;
}

var transformCallback = {};

var hasRequiredTransformCallback;

function requireTransformCallback () {
	if (hasRequiredTransformCallback) return transformCallback;
	hasRequiredTransformCallback = 1;

	Object.defineProperty(transformCallback, "__esModule", {
	  value: true
	});
	transformCallback.createTransformCallback = void 0;
	var _factory = requireFactory$b();
	const name = 'transformCallback';
	const dependencies = ['typed'];
	transformCallback.createTransformCallback = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	         * Transforms the given callback function based on its type and number of arrays.
	         *
	         * @param {Function} callback - The callback function to transform.
	         * @param {number} numberOfArrays - The number of arrays to pass to the callback function.
	         * @returns {*} - The transformed callback function.
	         */
	  return function (callback, numberOfArrays) {
	    if (typed.isTypedFunction(callback)) {
	      return _transformTypedCallbackFunction(callback, numberOfArrays);
	    } else {
	      return _transformCallbackFunction(callback, callback.length, numberOfArrays);
	    }
	  };

	  /**
	       * Transforms the given typed callback function based on the number of arrays.
	       *
	       * @param {Function} typedFunction - The typed callback function to transform.
	       * @param {number} numberOfArrays - The number of arrays to pass to the callback function.
	       * @returns {*} - The transformed callback function.
	       */
	  function _transformTypedCallbackFunction(typedFunction, numberOfArrays) {
	    const signatures = Object.fromEntries(Object.entries(typedFunction.signatures).map(_ref2 => {
	      let [signature, callbackFunction] = _ref2;
	      const numberOfCallbackInputs = signature.split(',').length;
	      if (typed.isTypedFunction(callbackFunction)) {
	        return [signature, _transformTypedCallbackFunction(callbackFunction, numberOfArrays)];
	      } else {
	        return [signature, _transformCallbackFunction(callbackFunction, numberOfCallbackInputs, numberOfArrays)];
	      }
	    }));
	    if (typeof typedFunction.name === 'string') {
	      return typed(typedFunction.name, signatures);
	    } else {
	      return typed(signatures);
	    }
	  }
	});

	/**
	     * Transforms the callback function based on the number of callback inputs and arrays.
	     * There are three cases:
	     * 1. The callback function has N arguments.
	     * 2. The callback function has N+1 arguments.
	     * 3. The callback function has 2N+1 arguments.
	     *
	     * @param {Function} callbackFunction - The callback function to transform.
	     * @param {number} numberOfCallbackInputs - The number of callback inputs.
	     * @param {number} numberOfArrays - The number of arrays.
	     * @returns {Function} The transformed callback function.
	     */
	function _transformCallbackFunction(callbackFunction, numberOfCallbackInputs, numberOfArrays) {
	  if (numberOfCallbackInputs === numberOfArrays) {
	    return callbackFunction;
	  } else if (numberOfCallbackInputs === numberOfArrays + 1) {
	    return function () {
	      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
	        args[_key] = arguments[_key];
	      }
	      const vals = args.slice(0, numberOfArrays);
	      const idx = _transformDims(args[numberOfArrays]);
	      return callbackFunction(...vals, idx);
	    };
	  } else if (numberOfCallbackInputs > numberOfArrays + 1) {
	    return function () {
	      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	        args[_key2] = arguments[_key2];
	      }
	      const vals = args.slice(0, numberOfArrays);
	      const idx = _transformDims(args[numberOfArrays]);
	      const rest = args.slice(numberOfArrays + 1);
	      return callbackFunction(...vals, idx, ...rest);
	    };
	  } else {
	    return callbackFunction;
	  }
	}

	/**
	   * Transforms the dimensions by adding 1 to each dimension.
	   *
	   * @param {Array} dims - The dimensions to transform.
	   * @returns {Array} The transformed dimensions.
	   */
	function _transformDims(dims) {
	  return dims.map(dim => dim + 1);
	}
	return transformCallback;
}

var hasRequiredFilter_transform;

function requireFilter_transform () {
	if (hasRequiredFilter_transform) return filter_transform;
	hasRequiredFilter_transform = 1;

	Object.defineProperty(filter_transform, "__esModule", {
	  value: true
	});
	filter_transform.createFilterTransform = void 0;
	var _filter = requireFilter$1();
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _compileInlineExpression = requireCompileInlineExpression();
	var _transformCallback = requireTransformCallback();
	const name = 'filter';
	const dependencies = ['typed'];
	filter_transform.createFilterTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Attach a transform function to math.filter
	   * Adds a property transform containing the transform function.
	   *
	   * This transform adds support for equations as test function for math.filter,
	   * so you can do something like 'filter([3, -2, 5], x > 0)'.
	   */
	  function filterTransform(args, math, scope) {
	    const filter = (0, _filter.createFilter)({
	      typed
	    });
	    const transformCallback = (0, _transformCallback.createTransformCallback)({
	      typed
	    });
	    if (args.length === 0) {
	      return filter();
	    }
	    let x = args[0];
	    if (args.length === 1) {
	      return filter(x);
	    }
	    const N = args.length - 1;
	    let callback = args[N];
	    if (x) {
	      x = _compileAndEvaluate(x, scope);
	    }
	    if (callback) {
	      if ((0, _is.isSymbolNode)(callback) || (0, _is.isFunctionAssignmentNode)(callback)) {
	        // a function pointer, like filter([3, -2, 5], myTestFunction)
	        callback = _compileAndEvaluate(callback, scope);
	      } else {
	        // an expression like filter([3, -2, 5], x > 0)
	        callback = (0, _compileInlineExpression.compileInlineExpression)(callback, math, scope);
	      }
	    }
	    return filter(x, transformCallback(callback, N));
	  }
	  filterTransform.rawArgs = true;
	  function _compileAndEvaluate(arg, scope) {
	    return arg.compile().evaluate(scope);
	  }
	  return filterTransform;
	}, {
	  isTransformFunction: true
	});
	return filter_transform;
}

var forEach_transform = {};

var hasRequiredForEach_transform;

function requireForEach_transform () {
	if (hasRequiredForEach_transform) return forEach_transform;
	hasRequiredForEach_transform = 1;

	Object.defineProperty(forEach_transform, "__esModule", {
	  value: true
	});
	forEach_transform.createForEachTransform = void 0;
	var _forEach = requireForEach$1();
	var _transformCallback = requireTransformCallback();
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _compileInlineExpression = requireCompileInlineExpression();
	const name = 'forEach';
	const dependencies = ['typed'];
	forEach_transform.createForEachTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Attach a transform function to math.forEach
	   * Adds a property transform containing the transform function.
	   *
	   * This transform creates a one-based index instead of a zero-based index
	   */
	  const forEach = (0, _forEach.createForEach)({
	    typed
	  });
	  const transformCallback = (0, _transformCallback.createTransformCallback)({
	    typed
	  });
	  function forEachTransform(args, math, scope) {
	    if (args.length === 0) {
	      return forEach();
	    }
	    let x = args[0];
	    if (args.length === 1) {
	      return forEach(x);
	    }
	    const N = args.length - 1;
	    let callback = args[N];
	    if (x) {
	      x = _compileAndEvaluate(x, scope);
	    }
	    if (callback) {
	      if ((0, _is.isSymbolNode)(callback) || (0, _is.isFunctionAssignmentNode)(callback)) {
	        // a function pointer, like filter([3, -2, 5], myTestFunction)
	        callback = _compileAndEvaluate(callback, scope);
	      } else {
	        // an expression like filter([3, -2, 5], x > 0)
	        callback = (0, _compileInlineExpression.compileInlineExpression)(callback, math, scope);
	      }
	    }
	    return forEach(x, transformCallback(callback, N));
	  }
	  forEachTransform.rawArgs = true;
	  function _compileAndEvaluate(arg, scope) {
	    return arg.compile().evaluate(scope);
	  }
	  return forEachTransform;
	}, {
	  isTransformFunction: true
	});
	return forEach_transform;
}

var index_transform = {};

var hasRequiredIndex_transform;

function requireIndex_transform () {
	if (hasRequiredIndex_transform) return index_transform;
	hasRequiredIndex_transform = 1;

	Object.defineProperty(index_transform, "__esModule", {
	  value: true
	});
	index_transform.createIndexTransform = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	const name = 'index';
	const dependencies = ['Index', 'getMatrixDataType'];
	index_transform.createIndexTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    Index,
	    getMatrixDataType
	  } = _ref;
	  /**
	   * Attach a transform function to math.index
	   * Adds a property transform containing the transform function.
	   *
	   * This transform creates a one-based index instead of a zero-based index
	   */
	  return function indexTransform() {
	    const args = [];
	    for (let i = 0, ii = arguments.length; i < ii; i++) {
	      let arg = arguments[i];

	      // change from one-based to zero based, convert BigNumber to number and leave Array of Booleans as is
	      if ((0, _is.isRange)(arg)) {
	        arg.start--;
	        arg.end -= arg.step > 0 ? 0 : 2;
	      } else if (arg && arg.isSet === true) {
	        arg = arg.map(function (v) {
	          return v - 1;
	        });
	      } else if ((0, _is.isArray)(arg) || (0, _is.isMatrix)(arg)) {
	        if (getMatrixDataType(arg) !== 'boolean') {
	          arg = arg.map(function (v) {
	            return v - 1;
	          });
	        }
	      } else if ((0, _is.isNumber)(arg) || (0, _is.isBigInt)(arg)) {
	        arg--;
	      } else if ((0, _is.isBigNumber)(arg)) {
	        arg = arg.toNumber() - 1;
	      } else if (typeof arg === 'string') ; else {
	        throw new TypeError('Dimension must be an Array, Matrix, number, bigint, string, or Range');
	      }
	      args[i] = arg;
	    }
	    const res = new Index();
	    Index.apply(res, args);
	    return res;
	  };
	}, {
	  isTransformFunction: true
	});
	return index_transform;
}

var map_transform = {};

var hasRequiredMap_transform;

function requireMap_transform () {
	if (hasRequiredMap_transform) return map_transform;
	hasRequiredMap_transform = 1;

	Object.defineProperty(map_transform, "__esModule", {
	  value: true
	});
	map_transform.createMapTransform = void 0;
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _map = requireMap$1();
	var _compileInlineExpression = requireCompileInlineExpression();
	var _transformCallback = requireTransformCallback();
	const name = 'map';
	const dependencies = ['typed'];
	map_transform.createMapTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed
	  } = _ref;
	  /**
	   * Attach a transform function to math.map
	   * Adds a property transform containing the transform function.
	   *
	   * This transform creates a one-based index instead of a zero-based index
	   */
	  const map = (0, _map.createMap)({
	    typed
	  });
	  const transformCallback = (0, _transformCallback.createTransformCallback)({
	    typed
	  });
	  function mapTransform(args, math, scope) {
	    if (args.length === 0) {
	      return map();
	    }
	    if (args.length === 1) {
	      return map(args[0]);
	    }
	    const N = args.length - 1;
	    let X = args.slice(0, N);
	    let callback = args[N];
	    X = X.map(arg => _compileAndEvaluate(arg, scope));
	    if (callback) {
	      if ((0, _is.isSymbolNode)(callback) || (0, _is.isFunctionAssignmentNode)(callback)) {
	        // a function pointer, like filter([3, -2, 5], myTestFunction)
	        callback = _compileAndEvaluate(callback, scope);
	      } else {
	        // an expression like filter([3, -2, 5], x > 0)
	        callback = (0, _compileInlineExpression.compileInlineExpression)(callback, math, scope);
	      }
	    }
	    return map(...X, transformCallback(callback, N));
	    function _compileAndEvaluate(arg, scope) {
	      return arg.compile().evaluate(scope);
	    }
	  }
	  mapTransform.rawArgs = true;
	  return mapTransform;
	}, {
	  isTransformFunction: true
	});
	return map_transform;
}

var max_transform = {};

var lastDimToZeroBase = {};

var dimToZeroBase = {};

var hasRequiredDimToZeroBase;

function requireDimToZeroBase () {
	if (hasRequiredDimToZeroBase) return dimToZeroBase;
	hasRequiredDimToZeroBase = 1;

	Object.defineProperty(dimToZeroBase, "__esModule", {
	  value: true
	});
	dimToZeroBase.dimToZeroBase = dimToZeroBase$1;
	dimToZeroBase.isNumberOrBigNumber = isNumberOrBigNumber;
	var _is = requireIs();
	/**
	 * Change last argument dim from one-based to zero-based.
	 */
	function dimToZeroBase$1(dim) {
	  if ((0, _is.isNumber)(dim)) {
	    return dim - 1;
	  } else if ((0, _is.isBigNumber)(dim)) {
	    return dim.minus(1);
	  } else {
	    return dim;
	  }
	}
	function isNumberOrBigNumber(n) {
	  return (0, _is.isNumber)(n) || (0, _is.isBigNumber)(n);
	}
	return dimToZeroBase;
}

var hasRequiredLastDimToZeroBase;

function requireLastDimToZeroBase () {
	if (hasRequiredLastDimToZeroBase) return lastDimToZeroBase;
	hasRequiredLastDimToZeroBase = 1;

	Object.defineProperty(lastDimToZeroBase, "__esModule", {
	  value: true
	});
	lastDimToZeroBase.lastDimToZeroBase = lastDimToZeroBase$1;
	var _is = requireIs();
	var _dimToZeroBase = requireDimToZeroBase();
	/**
	 * Change last argument dim from one-based to zero-based.
	 */
	function lastDimToZeroBase$1(args) {
	  if (args.length === 2 && (0, _is.isCollection)(args[0])) {
	    args = args.slice();
	    const dim = args[1];
	    if ((0, _dimToZeroBase.isNumberOrBigNumber)(dim)) {
	      args[1] = (0, _dimToZeroBase.dimToZeroBase)(dim);
	    }
	  }
	  return args;
	}
	return lastDimToZeroBase;
}

var hasRequiredMax_transform;

function requireMax_transform () {
	if (hasRequiredMax_transform) return max_transform;
	hasRequiredMax_transform = 1;

	Object.defineProperty(max_transform, "__esModule", {
	  value: true
	});
	max_transform.createMaxTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _max = requireMax$1();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'max';
	const dependencies = ['typed', 'config', 'numeric', 'larger', 'isNaN'];
	max_transform.createMaxTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    numeric,
	    larger,
	    isNaN: mathIsNaN
	  } = _ref;
	  const max = (0, _max.createMax)({
	    typed,
	    config,
	    numeric,
	    larger,
	    isNaN: mathIsNaN
	  });

	  /**
	   * Attach a transform function to math.max
	   * Adds a property transform containing the transform function.
	   *
	   * This transform changed the last `dim` parameter of function max
	   * from one-based to zero based
	   */
	  return typed('max', {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return max.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return max_transform;
}

var mean_transform = {};

var hasRequiredMean_transform;

function requireMean_transform () {
	if (hasRequiredMean_transform) return mean_transform;
	hasRequiredMean_transform = 1;

	Object.defineProperty(mean_transform, "__esModule", {
	  value: true
	});
	mean_transform.createMeanTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _mean = requireMean();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'mean';
	const dependencies = ['typed', 'add', 'divide'];
	mean_transform.createMeanTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    divide
	  } = _ref;
	  const mean = (0, _mean.createMean)({
	    typed,
	    add,
	    divide
	  });

	  /**
	   * Attach a transform function to math.mean
	   * Adds a property transform containing the transform function.
	   *
	   * This transform changed the last `dim` parameter of function mean
	   * from one-based to zero based
	   */
	  return typed('mean', {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return mean.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return mean_transform;
}

var min_transform = {};

var hasRequiredMin_transform;

function requireMin_transform () {
	if (hasRequiredMin_transform) return min_transform;
	hasRequiredMin_transform = 1;

	Object.defineProperty(min_transform, "__esModule", {
	  value: true
	});
	min_transform.createMinTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _min = requireMin$1();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'min';
	const dependencies = ['typed', 'config', 'numeric', 'smaller', 'isNaN'];
	min_transform.createMinTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    numeric,
	    smaller,
	    isNaN: mathIsNaN
	  } = _ref;
	  const min = (0, _min.createMin)({
	    typed,
	    config,
	    numeric,
	    smaller,
	    isNaN: mathIsNaN
	  });

	  /**
	   * Attach a transform function to math.min
	   * Adds a property transform containing the transform function.
	   *
	   * This transform changed the last `dim` parameter of function min
	   * from one-based to zero based
	   */
	  return typed('min', {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return min.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return min_transform;
}

var range_transform = {};

var hasRequiredRange_transform;

function requireRange_transform () {
	if (hasRequiredRange_transform) return range_transform;
	hasRequiredRange_transform = 1;

	Object.defineProperty(range_transform, "__esModule", {
	  value: true
	});
	range_transform.createRangeTransform = void 0;
	var _factory = requireFactory$b();
	var _range = requireRange$1();
	const name = 'range';
	const dependencies = ['typed', 'config', '?matrix', '?bignumber', 'equal', 'smaller', 'smallerEq', 'larger', 'largerEq', 'add', 'isZero', 'isPositive'];
	range_transform.createRangeTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    matrix,
	    bignumber,
	    equal,
	    smaller,
	    smallerEq,
	    larger,
	    largerEq,
	    add,
	    isZero,
	    isPositive
	  } = _ref;
	  const range = (0, _range.createRange)({
	    typed,
	    config,
	    matrix,
	    bignumber,
	    equal,
	    smaller,
	    smallerEq,
	    larger,
	    largerEq,
	    add,
	    isZero,
	    isPositive
	  });

	  /**
	   * Attach a transform function to math.range
	   * Adds a property transform containing the transform function.
	   *
	   * This transform creates a range which includes the end value
	   */
	  return typed('range', {
	    '...any': function (args) {
	      const lastIndex = args.length - 1;
	      const last = args[lastIndex];
	      if (typeof last !== 'boolean') {
	        // append a parameter includeEnd=true
	        args.push(true);
	      }
	      return range.apply(null, args);
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return range_transform;
}

var row_transform = {};

var hasRequiredRow_transform;

function requireRow_transform () {
	if (hasRequiredRow_transform) return row_transform;
	hasRequiredRow_transform = 1;

	Object.defineProperty(row_transform, "__esModule", {
	  value: true
	});
	row_transform.createRowTransform = void 0;
	var _factory = requireFactory$b();
	var _row = requireRow$1();
	var _errorTransform = requireErrorTransform();
	var _is = requireIs();
	const name = 'row';
	const dependencies = ['typed', 'Index', 'matrix', 'range'];

	/**
	 * Attach a transform function to matrix.column
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the last `index` parameter of function column
	 * from zero-based to one-based
	 */
	row_transform.createRowTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    Index,
	    matrix,
	    range
	  } = _ref;
	  const row = (0, _row.createRow)({
	    typed,
	    Index,
	    matrix,
	    range
	  });

	  // @see: comment of row itself
	  return typed('row', {
	    '...any': function (args) {
	      // change last argument from zero-based to one-based
	      const lastIndex = args.length - 1;
	      const last = args[lastIndex];
	      if ((0, _is.isNumber)(last)) {
	        args[lastIndex] = last - 1;
	      }
	      try {
	        return row.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return row_transform;
}

var subset_transform = {};

var hasRequiredSubset_transform;

function requireSubset_transform () {
	if (hasRequiredSubset_transform) return subset_transform;
	hasRequiredSubset_transform = 1;

	Object.defineProperty(subset_transform, "__esModule", {
	  value: true
	});
	subset_transform.createSubsetTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _subset = requireSubset$1();
	const name = 'subset';
	const dependencies = ['typed', 'matrix', 'zeros', 'add'];
	subset_transform.createSubsetTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    zeros,
	    add
	  } = _ref;
	  const subset = (0, _subset.createSubset)({
	    typed,
	    matrix,
	    zeros,
	    add
	  });

	  /**
	   * Attach a transform function to math.subset
	   * Adds a property transform containing the transform function.
	   *
	   * This transform creates a range which includes the end value
	   */
	  return typed('subset', {
	    '...any': function (args) {
	      try {
	        return subset.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return subset_transform;
}

var concat_transform = {};

var hasRequiredConcat_transform;

function requireConcat_transform () {
	if (hasRequiredConcat_transform) return concat_transform;
	hasRequiredConcat_transform = 1;

	Object.defineProperty(concat_transform, "__esModule", {
	  value: true
	});
	concat_transform.createConcatTransform = void 0;
	var _is = requireIs();
	var _errorTransform = requireErrorTransform();
	var _factory = requireFactory$b();
	var _concat = requireConcat$1();
	const name = 'concat';
	const dependencies = ['typed', 'matrix', 'isInteger'];
	concat_transform.createConcatTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    isInteger
	  } = _ref;
	  const concat = (0, _concat.createConcat)({
	    typed,
	    matrix,
	    isInteger
	  });

	  /**
	   * Attach a transform function to math.range
	   * Adds a property transform containing the transform function.
	   *
	   * This transform changed the last `dim` parameter of function concat
	   * from one-based to zero based
	   */
	  return typed('concat', {
	    '...any': function (args) {
	      // change last argument from one-based to zero-based
	      const lastIndex = args.length - 1;
	      const last = args[lastIndex];
	      if ((0, _is.isNumber)(last)) {
	        args[lastIndex] = last - 1;
	      } else if ((0, _is.isBigNumber)(last)) {
	        args[lastIndex] = last.minus(1);
	      }
	      try {
	        return concat.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return concat_transform;
}

var diff_transform = {};

var hasRequiredDiff_transform;

function requireDiff_transform () {
	if (hasRequiredDiff_transform) return diff_transform;
	hasRequiredDiff_transform = 1;

	Object.defineProperty(diff_transform, "__esModule", {
	  value: true
	});
	diff_transform.createDiffTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _diff = requireDiff$1();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'diff';
	const dependencies = ['typed', 'matrix', 'subtract', 'number', 'bignumber'];
	diff_transform.createDiffTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    subtract,
	    number,
	    bignumber
	  } = _ref;
	  const diff = (0, _diff.createDiff)({
	    typed,
	    matrix,
	    subtract,
	    number,
	    bignumber
	  });

	  /**
	   * Attach a transform function to math.diff
	   * Adds a property transform containing the transform function.
	   *
	   * This transform creates a range which includes the end value
	   */
	  return typed(name, {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return diff.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return diff_transform;
}

var std_transform = {};

var hasRequiredStd_transform;

function requireStd_transform () {
	if (hasRequiredStd_transform) return std_transform;
	hasRequiredStd_transform = 1;

	Object.defineProperty(std_transform, "__esModule", {
	  value: true
	});
	std_transform.createStdTransform = void 0;
	var _factory = requireFactory$b();
	var _std = requireStd();
	var _errorTransform = requireErrorTransform();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'std';
	const dependencies = ['typed', 'map', 'sqrt', 'variance'];

	/**
	 * Attach a transform function to math.std
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the `dim` parameter of function std
	 * from one-based to zero based
	 */
	std_transform.createStdTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    map,
	    sqrt,
	    variance
	  } = _ref;
	  const std = (0, _std.createStd)({
	    typed,
	    map,
	    sqrt,
	    variance
	  });
	  return typed('std', {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return std.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return std_transform;
}

var sum_transform = {};

var hasRequiredSum_transform;

function requireSum_transform () {
	if (hasRequiredSum_transform) return sum_transform;
	hasRequiredSum_transform = 1;

	Object.defineProperty(sum_transform, "__esModule", {
	  value: true
	});
	sum_transform.createSumTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _sum = requireSum();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	/**
	 * Attach a transform function to math.sum
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the last `dim` parameter of function sum
	 * from one-based to zero based
	 */
	const name = 'sum';
	const dependencies = ['typed', 'config', 'add', 'numeric'];
	sum_transform.createSumTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    config,
	    add,
	    numeric
	  } = _ref;
	  const sum = (0, _sum.createSum)({
	    typed,
	    config,
	    add,
	    numeric
	  });
	  return typed(name, {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return sum.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return sum_transform;
}

var quantileSeq_transform = {};

var hasRequiredQuantileSeq_transform;

function requireQuantileSeq_transform () {
	if (hasRequiredQuantileSeq_transform) return quantileSeq_transform;
	hasRequiredQuantileSeq_transform = 1;

	Object.defineProperty(quantileSeq_transform, "__esModule", {
	  value: true
	});
	quantileSeq_transform.createQuantileSeqTransform = void 0;
	var _factory = requireFactory$b();
	var _quantileSeq = requireQuantileSeq();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'quantileSeq';
	const dependencies = ['typed', 'bignumber', 'add', 'subtract', 'divide', 'multiply', 'partitionSelect', 'compare', 'isInteger', 'smaller', 'smallerEq', 'larger', 'mapSlices'];

	/**
	 * Attach a transform function to math.quantileSeq
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the `dim` parameter of function std
	 * from one-based to zero based
	 */
	quantileSeq_transform.createQuantileSeqTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    bignumber,
	    add,
	    subtract,
	    divide,
	    multiply,
	    partitionSelect,
	    compare,
	    isInteger,
	    smaller,
	    smallerEq,
	    larger,
	    mapSlices
	  } = _ref;
	  const quantileSeq = (0, _quantileSeq.createQuantileSeq)({
	    typed,
	    bignumber,
	    add,
	    subtract,
	    divide,
	    multiply,
	    partitionSelect,
	    compare,
	    isInteger,
	    smaller,
	    smallerEq,
	    larger,
	    mapSlices
	  });
	  return typed('quantileSeq', {
	    'Array | Matrix, number | BigNumber': quantileSeq,
	    'Array | Matrix, number | BigNumber, number': (arr, prob, dim) => quantileSeq(arr, prob, dimToZeroBase(dim)),
	    'Array | Matrix, number | BigNumber, boolean': quantileSeq,
	    'Array | Matrix, number | BigNumber, boolean, number': (arr, prob, sorted, dim) => quantileSeq(arr, prob, sorted, dimToZeroBase(dim)),
	    'Array | Matrix, Array | Matrix': quantileSeq,
	    'Array | Matrix, Array | Matrix, number': (data, prob, dim) => quantileSeq(data, prob, dimToZeroBase(dim)),
	    'Array | Matrix, Array | Matrix, boolean': quantileSeq,
	    'Array | Matrix, Array | Matrix, boolean, number': (data, prob, sorted, dim) => quantileSeq(data, prob, sorted, dimToZeroBase(dim))
	  });
	  function dimToZeroBase(dim) {
	    // TODO: find a better way, maybe lastDimToZeroBase could apply to more cases.
	    return (0, _lastDimToZeroBase.lastDimToZeroBase)([[], dim])[1];
	  }
	}, {
	  isTransformFunction: true
	});
	return quantileSeq_transform;
}

var cumsum_transform = {};

var hasRequiredCumsum_transform;

function requireCumsum_transform () {
	if (hasRequiredCumsum_transform) return cumsum_transform;
	hasRequiredCumsum_transform = 1;

	Object.defineProperty(cumsum_transform, "__esModule", {
	  value: true
	});
	cumsum_transform.createCumSumTransform = void 0;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _cumsum = requireCumsum();
	/**
	 * Attach a transform function to math.sum
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the last `dim` parameter of function sum
	 * from one-based to zero based
	 */
	const name = 'cumsum';
	const dependencies = ['typed', 'add', 'unaryPlus'];
	cumsum_transform.createCumSumTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    unaryPlus
	  } = _ref;
	  const cumsum = (0, _cumsum.createCumSum)({
	    typed,
	    add,
	    unaryPlus
	  });
	  return typed(name, {
	    '...any': function (args) {
	      // change last argument dim from one-based to zero-based
	      if (args.length === 2 && (0, _is.isCollection)(args[0])) {
	        const dim = args[1];
	        if ((0, _is.isNumber)(dim)) {
	          args[1] = dim - 1;
	        } else if ((0, _is.isBigNumber)(dim)) {
	          args[1] = dim.minus(1);
	        }
	      }
	      try {
	        return cumsum.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return cumsum_transform;
}

var variance_transform = {};

var hasRequiredVariance_transform;

function requireVariance_transform () {
	if (hasRequiredVariance_transform) return variance_transform;
	hasRequiredVariance_transform = 1;

	Object.defineProperty(variance_transform, "__esModule", {
	  value: true
	});
	variance_transform.createVarianceTransform = void 0;
	var _factory = requireFactory$b();
	var _errorTransform = requireErrorTransform();
	var _variance = requireVariance();
	var _lastDimToZeroBase = requireLastDimToZeroBase();
	const name = 'variance';
	const dependencies = ['typed', 'add', 'subtract', 'multiply', 'divide', 'mapSlices', 'isNaN'];

	/**
	 * Attach a transform function to math.var
	 * Adds a property transform containing the transform function.
	 *
	 * This transform changed the `dim` parameter of function var
	 * from one-based to zero based
	 */
	variance_transform.createVarianceTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    add,
	    subtract,
	    multiply,
	    divide,
	    mapSlices,
	    isNaN: mathIsNaN
	  } = _ref;
	  const variance = (0, _variance.createVariance)({
	    typed,
	    add,
	    subtract,
	    multiply,
	    divide,
	    mapSlices,
	    isNaN: mathIsNaN
	  });
	  return typed(name, {
	    '...any': function (args) {
	      args = (0, _lastDimToZeroBase.lastDimToZeroBase)(args);
	      try {
	        return variance.apply(null, args);
	      } catch (err) {
	        throw (0, _errorTransform.errorTransform)(err);
	      }
	    }
	  });
	}, {
	  isTransformFunction: true
	});
	return variance_transform;
}

var print_transform = {};

var hasRequiredPrint_transform;

function requirePrint_transform () {
	if (hasRequiredPrint_transform) return print_transform;
	hasRequiredPrint_transform = 1;

	Object.defineProperty(print_transform, "__esModule", {
	  value: true
	});
	print_transform.createPrintTransform = void 0;
	var _print = requirePrint$1();
	var _factory = requireFactory$b();
	var _print2 = requirePrint$2();
	const name = 'print';
	const dependencies = ['typed', 'matrix', 'zeros', 'add'];
	print_transform.createPrintTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    zeros,
	    add
	  } = _ref;
	  const print = (0, _print.createPrint)({
	    typed,
	    matrix,
	    zeros,
	    add
	  });
	  return typed(name, {
	    'string, Object | Array': function (template, values) {
	      return print(_convertTemplateToZeroBasedIndex(template), values);
	    },
	    'string, Object | Array, number | Object': function (template, values, options) {
	      return print(_convertTemplateToZeroBasedIndex(template), values, options);
	    }
	  });
	  function _convertTemplateToZeroBasedIndex(template) {
	    return template.replace(_print2.printTemplate, x => {
	      const parts = x.slice(1).split('.');
	      const result = parts.map(function (part) {
	        if (!isNaN(part) && part.length > 0) {
	          return parseInt(part) - 1;
	        } else {
	          return part;
	        }
	      });
	      return '$' + result.join('.');
	    });
	  }
	}, {
	  isTransformFunction: true
	});
	return print_transform;
}

var and_transform = {};

var hasRequiredAnd_transform;

function requireAnd_transform () {
	if (hasRequiredAnd_transform) return and_transform;
	hasRequiredAnd_transform = 1;

	Object.defineProperty(and_transform, "__esModule", {
	  value: true
	});
	and_transform.createAndTransform = void 0;
	var _and = requireAnd$1();
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'and';
	const dependencies = ['typed', 'matrix', 'zeros', 'add', 'equalScalar', 'not', 'concat'];
	and_transform.createAndTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    not,
	    concat
	  } = _ref;
	  const and = (0, _and.createAnd)({
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    not,
	    concat
	  });
	  function andTransform(args, math, scope) {
	    const condition1 = args[0].compile().evaluate(scope);
	    if (!(0, _is.isCollection)(condition1) && !and(condition1, true)) {
	      return false;
	    }
	    const condition2 = args[1].compile().evaluate(scope);
	    return and(condition1, condition2);
	  }
	  andTransform.rawArgs = true;
	  return andTransform;
	}, {
	  isTransformFunction: true
	});
	return and_transform;
}

var or_transform = {};

var hasRequiredOr_transform;

function requireOr_transform () {
	if (hasRequiredOr_transform) return or_transform;
	hasRequiredOr_transform = 1;

	Object.defineProperty(or_transform, "__esModule", {
	  value: true
	});
	or_transform.createOrTransform = void 0;
	var _or = requireOr$1();
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'or';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'DenseMatrix', 'concat'];
	or_transform.createOrTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const or = (0, _or.createOr)({
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat
	  });
	  function orTransform(args, math, scope) {
	    const condition1 = args[0].compile().evaluate(scope);
	    if (!(0, _is.isCollection)(condition1) && or(condition1, false)) {
	      return true;
	    }
	    const condition2 = args[1].compile().evaluate(scope);
	    return or(condition1, condition2);
	  }
	  orTransform.rawArgs = true;
	  return orTransform;
	}, {
	  isTransformFunction: true
	});
	return or_transform;
}

var nullish_transform = {};

var hasRequiredNullish_transform;

function requireNullish_transform () {
	if (hasRequiredNullish_transform) return nullish_transform;
	hasRequiredNullish_transform = 1;

	Object.defineProperty(nullish_transform, "__esModule", {
	  value: true
	});
	nullish_transform.createNullishTransform = void 0;
	var _nullish = requireNullish$1();
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'nullish';
	const dependencies = ['typed', 'matrix', 'size', 'flatten', 'deepEqual'];
	nullish_transform.createNullishTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    size,
	    flatten,
	    deepEqual
	  } = _ref;
	  const nullish = (0, _nullish.createNullish)({
	    typed,
	    matrix,
	    size,
	    flatten,
	    deepEqual
	  });
	  function nullishTransform(args, math, scope) {
	    const left = args[0].compile().evaluate(scope);

	    // If left is not a collection and not nullish, short-circuit and return it
	    if (!(0, _is.isCollection)(left) && left != null && left !== undefined) {
	      return left;
	    }

	    // Otherwise evaluate right and apply full nullish semantics (incl. element-wise)
	    const right = args[1].compile().evaluate(scope);
	    return nullish(left, right);
	  }
	  nullishTransform.rawArgs = true;
	  return nullishTransform;
	}, {
	  isTransformFunction: true
	});
	return nullish_transform;
}

var bitAnd_transform = {};

var hasRequiredBitAnd_transform;

function requireBitAnd_transform () {
	if (hasRequiredBitAnd_transform) return bitAnd_transform;
	hasRequiredBitAnd_transform = 1;

	Object.defineProperty(bitAnd_transform, "__esModule", {
	  value: true
	});
	bitAnd_transform.createBitAndTransform = void 0;
	var _bitAnd = requireBitAnd$1();
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'bitAnd';
	const dependencies = ['typed', 'matrix', 'zeros', 'add', 'equalScalar', 'not', 'concat'];
	bitAnd_transform.createBitAndTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    not,
	    concat
	  } = _ref;
	  const bitAnd = (0, _bitAnd.createBitAnd)({
	    typed,
	    matrix,
	    equalScalar,
	    zeros,
	    not,
	    concat
	  });
	  function bitAndTransform(args, math, scope) {
	    const condition1 = args[0].compile().evaluate(scope);
	    if (!(0, _is.isCollection)(condition1)) {
	      if (isNaN(condition1)) {
	        return NaN;
	      }
	      if (condition1 === 0 || condition1 === false) {
	        return 0;
	      }
	    }
	    const condition2 = args[1].compile().evaluate(scope);
	    return bitAnd(condition1, condition2);
	  }
	  bitAndTransform.rawArgs = true;
	  return bitAndTransform;
	}, {
	  isTransformFunction: true
	});
	return bitAnd_transform;
}

var bitOr_transform = {};

var hasRequiredBitOr_transform;

function requireBitOr_transform () {
	if (hasRequiredBitOr_transform) return bitOr_transform;
	hasRequiredBitOr_transform = 1;

	Object.defineProperty(bitOr_transform, "__esModule", {
	  value: true
	});
	bitOr_transform.createBitOrTransform = void 0;
	var _bitOr = requireBitOr$1();
	var _factory = requireFactory$b();
	var _is = requireIs();
	const name = 'bitOr';
	const dependencies = ['typed', 'matrix', 'equalScalar', 'DenseMatrix', 'concat'];
	bitOr_transform.createBitOrTransform = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat
	  } = _ref;
	  const bitOr = (0, _bitOr.createBitOr)({
	    typed,
	    matrix,
	    equalScalar,
	    DenseMatrix,
	    concat
	  });
	  function bitOrTransform(args, math, scope) {
	    const condition1 = args[0].compile().evaluate(scope);
	    if (!(0, _is.isCollection)(condition1)) {
	      if (isNaN(condition1)) {
	        return NaN;
	      }
	      if (condition1 === -1) {
	        return -1;
	      }
	      if (condition1 === true) {
	        return 1;
	      }
	    }
	    const condition2 = args[1].compile().evaluate(scope);
	    return bitOr(condition1, condition2);
	  }
	  bitOrTransform.rawArgs = true;
	  return bitOrTransform;
	}, {
	  isTransformFunction: true
	});
	return bitOr_transform;
}

var num = {};

var hasRequiredNum;

function requireNum () {
	if (hasRequiredNum) return num;
	hasRequiredNum = 1;

	Object.defineProperty(num, "__esModule", {
	  value: true
	});
	num.createNum = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'num';
	const dependencies = ['typed', 'fraction'];
	num.createNum = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    fraction
	  } = _ref;
	  /**
	   * Get the numerator of a fraction.
	   * For a fraction `a/b`, the function returns `a`.
	   *
	   * The result is always in lowest terms. For example, `num(fraction(8, 6))`
	   * returns `4n` because 8/6 simplifies to 4/3.
	   *
	   * For negative fractions like `-a/b` or `a/-b`, the sign is always
	   * included in the numerator. Both forms are normalized internally, so
	   * `num(fraction(-2, 3))` and `num(fraction(2, -3))` both return `-2`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.num(x)
	   *
	   * Examples:
	   *
	   *    math.num(math.fraction(2, 3))   // returns 2n
	   *    math.num(math.fraction(8, 6))   // returns 4n
	   *    math.num(math.fraction('5/8'))  // returns 5n
	   *    math.num(math.fraction(-2, 3))  // returns -2n
	   *    math.num(math.fraction(2, -3))  // returns -2n
	   *    math.num(math.bignumber('0.5'))  // returns 1n
	   *
	   * See also:
	   *
	   *    den, fraction
	   *
	   * History:
	   *
	   *    v15.2.0      Created
	   *
	   * @param {Fraction | BigNumber | Array | Matrix} x
	   *            A fraction, BigNumber, or array with fractions
	   * @return {bigint | Array | Matrix} The numerator of x (in lowest terms)
	   */
	  return typed(name, {
	    Fraction: x => x.s * x.n,
	    BigNumber: x => {
	      const f = fraction(x);
	      return f.s * f.n;
	    },
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return num;
}

var den = {};

var hasRequiredDen;

function requireDen () {
	if (hasRequiredDen) return den;
	hasRequiredDen = 1;

	Object.defineProperty(den, "__esModule", {
	  value: true
	});
	den.createDen = void 0;
	var _factory = requireFactory$b();
	var _collection = requireCollection();
	const name = 'den';
	const dependencies = ['typed', 'fraction'];
	den.createDen = /* #__PURE__ */(0, _factory.factory)(name, dependencies, _ref => {
	  let {
	    typed,
	    fraction
	  } = _ref;
	  /**
	   * Get the denominator of a fraction.
	   * For a fraction `a/b`, the function returns `b`.
	   *
	   * The result is always in lowest terms. For example, `den(fraction(8, 6))`
	   * returns `3n` because 8/6 simplifies to 4/3.
	   *
	   * For negative fractions like `-a/b` or `a/-b`, the denominator is
	   * always returned as a positive bigint. The sign is stored in the
	   * numerator. So `den(fraction(-2, 3))` and `den(fraction(2, -3))`
	   * both return `3n`.
	   *
	   * For matrices, the function is evaluated element wise.
	   *
	   * Syntax:
	   *
	   *    math.den(x)
	   *
	   * Examples:
	   *
	   *    math.den(math.fraction(2, 3))   // returns 3n
	   *    math.den(math.fraction(8, 6))   // returns 3n
	   *    math.den(math.fraction('5/8'))  // returns 8n
	   *    math.den(math.fraction(-2, 3))  // returns 3n
	   *    math.den(math.fraction(2, -3))  // returns 3n
	   *    math.den(math.bignumber('0.5'))  // returns 2n
	   *
	   * See also:
	   *
	   *    num, fraction
	   *
	   * History:
	   *
	   *    v15.2.0      Created
	   *
	   * @param {Fraction | BigNumber | Array | Matrix} x
	   *            A fraction, BigNumber, or array with fractions
	   * @return {bigint | Array | Matrix} The denominator of x (in lowest terms)
	   */
	  return typed(name, {
	    Fraction: x => x.d,
	    BigNumber: x => fraction(x).d,
	    'Array | Matrix': typed.referToSelf(self => x => (0, _collection.deepMap)(x, self))
	  });
	});
	return den;
}

var hasRequiredFactoriesAny;

function requireFactoriesAny () {
	if (hasRequiredFactoriesAny) return factoriesAny;
	hasRequiredFactoriesAny = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		Object.defineProperty(exports, "createAbs", {
		  enumerable: true,
		  get: function () {
		    return _abs.createAbs;
		  }
		});
		Object.defineProperty(exports, "createAccessorNode", {
		  enumerable: true,
		  get: function () {
		    return _AccessorNode.createAccessorNode;
		  }
		});
		Object.defineProperty(exports, "createAcos", {
		  enumerable: true,
		  get: function () {
		    return _acos.createAcos;
		  }
		});
		Object.defineProperty(exports, "createAcosh", {
		  enumerable: true,
		  get: function () {
		    return _acosh.createAcosh;
		  }
		});
		Object.defineProperty(exports, "createAcot", {
		  enumerable: true,
		  get: function () {
		    return _acot.createAcot;
		  }
		});
		Object.defineProperty(exports, "createAcoth", {
		  enumerable: true,
		  get: function () {
		    return _acoth.createAcoth;
		  }
		});
		Object.defineProperty(exports, "createAcsc", {
		  enumerable: true,
		  get: function () {
		    return _acsc.createAcsc;
		  }
		});
		Object.defineProperty(exports, "createAcsch", {
		  enumerable: true,
		  get: function () {
		    return _acsch.createAcsch;
		  }
		});
		Object.defineProperty(exports, "createAdd", {
		  enumerable: true,
		  get: function () {
		    return _add.createAdd;
		  }
		});
		Object.defineProperty(exports, "createAddScalar", {
		  enumerable: true,
		  get: function () {
		    return _addScalar.createAddScalar;
		  }
		});
		Object.defineProperty(exports, "createAnd", {
		  enumerable: true,
		  get: function () {
		    return _and.createAnd;
		  }
		});
		Object.defineProperty(exports, "createAndTransform", {
		  enumerable: true,
		  get: function () {
		    return _andTransform.createAndTransform;
		  }
		});
		Object.defineProperty(exports, "createArg", {
		  enumerable: true,
		  get: function () {
		    return _arg.createArg;
		  }
		});
		Object.defineProperty(exports, "createArrayNode", {
		  enumerable: true,
		  get: function () {
		    return _ArrayNode.createArrayNode;
		  }
		});
		Object.defineProperty(exports, "createAsec", {
		  enumerable: true,
		  get: function () {
		    return _asec.createAsec;
		  }
		});
		Object.defineProperty(exports, "createAsech", {
		  enumerable: true,
		  get: function () {
		    return _asech.createAsech;
		  }
		});
		Object.defineProperty(exports, "createAsin", {
		  enumerable: true,
		  get: function () {
		    return _asin.createAsin;
		  }
		});
		Object.defineProperty(exports, "createAsinh", {
		  enumerable: true,
		  get: function () {
		    return _asinh.createAsinh;
		  }
		});
		Object.defineProperty(exports, "createAssignmentNode", {
		  enumerable: true,
		  get: function () {
		    return _AssignmentNode.createAssignmentNode;
		  }
		});
		Object.defineProperty(exports, "createAtan", {
		  enumerable: true,
		  get: function () {
		    return _atan.createAtan;
		  }
		});
		Object.defineProperty(exports, "createAtan2", {
		  enumerable: true,
		  get: function () {
		    return _atan2.createAtan2;
		  }
		});
		Object.defineProperty(exports, "createAtanh", {
		  enumerable: true,
		  get: function () {
		    return _atanh.createAtanh;
		  }
		});
		Object.defineProperty(exports, "createAtomicMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createAtomicMass;
		  }
		});
		Object.defineProperty(exports, "createAvogadro", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createAvogadro;
		  }
		});
		Object.defineProperty(exports, "createBellNumbers", {
		  enumerable: true,
		  get: function () {
		    return _bellNumbers.createBellNumbers;
		  }
		});
		Object.defineProperty(exports, "createBernoulli", {
		  enumerable: true,
		  get: function () {
		    return _bernoulli.createBernoulli;
		  }
		});
		Object.defineProperty(exports, "createBigNumberClass", {
		  enumerable: true,
		  get: function () {
		    return _BigNumber.createBigNumberClass;
		  }
		});
		Object.defineProperty(exports, "createBigint", {
		  enumerable: true,
		  get: function () {
		    return _bigint.createBigint;
		  }
		});
		Object.defineProperty(exports, "createBignumber", {
		  enumerable: true,
		  get: function () {
		    return _bignumber.createBignumber;
		  }
		});
		Object.defineProperty(exports, "createBin", {
		  enumerable: true,
		  get: function () {
		    return _bin.createBin;
		  }
		});
		Object.defineProperty(exports, "createBitAnd", {
		  enumerable: true,
		  get: function () {
		    return _bitAnd.createBitAnd;
		  }
		});
		Object.defineProperty(exports, "createBitAndTransform", {
		  enumerable: true,
		  get: function () {
		    return _bitAndTransform.createBitAndTransform;
		  }
		});
		Object.defineProperty(exports, "createBitNot", {
		  enumerable: true,
		  get: function () {
		    return _bitNot.createBitNot;
		  }
		});
		Object.defineProperty(exports, "createBitOr", {
		  enumerable: true,
		  get: function () {
		    return _bitOr.createBitOr;
		  }
		});
		Object.defineProperty(exports, "createBitOrTransform", {
		  enumerable: true,
		  get: function () {
		    return _bitOrTransform.createBitOrTransform;
		  }
		});
		Object.defineProperty(exports, "createBitXor", {
		  enumerable: true,
		  get: function () {
		    return _bitXor.createBitXor;
		  }
		});
		Object.defineProperty(exports, "createBlockNode", {
		  enumerable: true,
		  get: function () {
		    return _BlockNode.createBlockNode;
		  }
		});
		Object.defineProperty(exports, "createBohrMagneton", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createBohrMagneton;
		  }
		});
		Object.defineProperty(exports, "createBohrRadius", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createBohrRadius;
		  }
		});
		Object.defineProperty(exports, "createBoltzmann", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createBoltzmann;
		  }
		});
		Object.defineProperty(exports, "createBoolean", {
		  enumerable: true,
		  get: function () {
		    return _boolean.createBoolean;
		  }
		});
		Object.defineProperty(exports, "createCatalan", {
		  enumerable: true,
		  get: function () {
		    return _catalan.createCatalan;
		  }
		});
		Object.defineProperty(exports, "createCbrt", {
		  enumerable: true,
		  get: function () {
		    return _cbrt.createCbrt;
		  }
		});
		Object.defineProperty(exports, "createCeil", {
		  enumerable: true,
		  get: function () {
		    return _ceil.createCeil;
		  }
		});
		Object.defineProperty(exports, "createChain", {
		  enumerable: true,
		  get: function () {
		    return _chain.createChain;
		  }
		});
		Object.defineProperty(exports, "createChainClass", {
		  enumerable: true,
		  get: function () {
		    return _Chain.createChainClass;
		  }
		});
		Object.defineProperty(exports, "createClassicalElectronRadius", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createClassicalElectronRadius;
		  }
		});
		Object.defineProperty(exports, "createClone", {
		  enumerable: true,
		  get: function () {
		    return _clone.createClone;
		  }
		});
		Object.defineProperty(exports, "createColumn", {
		  enumerable: true,
		  get: function () {
		    return _column.createColumn;
		  }
		});
		Object.defineProperty(exports, "createColumnTransform", {
		  enumerable: true,
		  get: function () {
		    return _columnTransform.createColumnTransform;
		  }
		});
		Object.defineProperty(exports, "createCombinations", {
		  enumerable: true,
		  get: function () {
		    return _combinations.createCombinations;
		  }
		});
		Object.defineProperty(exports, "createCombinationsWithRep", {
		  enumerable: true,
		  get: function () {
		    return _combinationsWithRep.createCombinationsWithRep;
		  }
		});
		Object.defineProperty(exports, "createCompare", {
		  enumerable: true,
		  get: function () {
		    return _compare.createCompare;
		  }
		});
		Object.defineProperty(exports, "createCompareNatural", {
		  enumerable: true,
		  get: function () {
		    return _compareNatural.createCompareNatural;
		  }
		});
		Object.defineProperty(exports, "createCompareText", {
		  enumerable: true,
		  get: function () {
		    return _compareText.createCompareText;
		  }
		});
		Object.defineProperty(exports, "createCompile", {
		  enumerable: true,
		  get: function () {
		    return _compile.createCompile;
		  }
		});
		Object.defineProperty(exports, "createComplex", {
		  enumerable: true,
		  get: function () {
		    return _complex.createComplex;
		  }
		});
		Object.defineProperty(exports, "createComplexClass", {
		  enumerable: true,
		  get: function () {
		    return _Complex.createComplexClass;
		  }
		});
		Object.defineProperty(exports, "createComposition", {
		  enumerable: true,
		  get: function () {
		    return _composition.createComposition;
		  }
		});
		Object.defineProperty(exports, "createConcat", {
		  enumerable: true,
		  get: function () {
		    return _concat.createConcat;
		  }
		});
		Object.defineProperty(exports, "createConcatTransform", {
		  enumerable: true,
		  get: function () {
		    return _concatTransform.createConcatTransform;
		  }
		});
		Object.defineProperty(exports, "createConditionalNode", {
		  enumerable: true,
		  get: function () {
		    return _ConditionalNode.createConditionalNode;
		  }
		});
		Object.defineProperty(exports, "createConductanceQuantum", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createConductanceQuantum;
		  }
		});
		Object.defineProperty(exports, "createConj", {
		  enumerable: true,
		  get: function () {
		    return _conj.createConj;
		  }
		});
		Object.defineProperty(exports, "createConstantNode", {
		  enumerable: true,
		  get: function () {
		    return _ConstantNode.createConstantNode;
		  }
		});
		Object.defineProperty(exports, "createCorr", {
		  enumerable: true,
		  get: function () {
		    return _corr.createCorr;
		  }
		});
		Object.defineProperty(exports, "createCos", {
		  enumerable: true,
		  get: function () {
		    return _cos.createCos;
		  }
		});
		Object.defineProperty(exports, "createCosh", {
		  enumerable: true,
		  get: function () {
		    return _cosh.createCosh;
		  }
		});
		Object.defineProperty(exports, "createCot", {
		  enumerable: true,
		  get: function () {
		    return _cot.createCot;
		  }
		});
		Object.defineProperty(exports, "createCoth", {
		  enumerable: true,
		  get: function () {
		    return _coth.createCoth;
		  }
		});
		Object.defineProperty(exports, "createCoulomb", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createCoulomb;
		  }
		});
		Object.defineProperty(exports, "createCoulombConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createCoulombConstant;
		  }
		});
		Object.defineProperty(exports, "createCount", {
		  enumerable: true,
		  get: function () {
		    return _count.createCount;
		  }
		});
		Object.defineProperty(exports, "createCreateUnit", {
		  enumerable: true,
		  get: function () {
		    return _createUnit.createCreateUnit;
		  }
		});
		Object.defineProperty(exports, "createCross", {
		  enumerable: true,
		  get: function () {
		    return _cross.createCross;
		  }
		});
		Object.defineProperty(exports, "createCsc", {
		  enumerable: true,
		  get: function () {
		    return _csc.createCsc;
		  }
		});
		Object.defineProperty(exports, "createCsch", {
		  enumerable: true,
		  get: function () {
		    return _csch.createCsch;
		  }
		});
		Object.defineProperty(exports, "createCtranspose", {
		  enumerable: true,
		  get: function () {
		    return _ctranspose.createCtranspose;
		  }
		});
		Object.defineProperty(exports, "createCube", {
		  enumerable: true,
		  get: function () {
		    return _cube.createCube;
		  }
		});
		Object.defineProperty(exports, "createCumSum", {
		  enumerable: true,
		  get: function () {
		    return _cumsum.createCumSum;
		  }
		});
		Object.defineProperty(exports, "createCumSumTransform", {
		  enumerable: true,
		  get: function () {
		    return _cumsumTransform.createCumSumTransform;
		  }
		});
		Object.defineProperty(exports, "createDeepEqual", {
		  enumerable: true,
		  get: function () {
		    return _deepEqual.createDeepEqual;
		  }
		});
		Object.defineProperty(exports, "createDen", {
		  enumerable: true,
		  get: function () {
		    return _den.createDen;
		  }
		});
		Object.defineProperty(exports, "createDenseMatrixClass", {
		  enumerable: true,
		  get: function () {
		    return _DenseMatrix.createDenseMatrixClass;
		  }
		});
		Object.defineProperty(exports, "createDerivative", {
		  enumerable: true,
		  get: function () {
		    return _derivative.createDerivative;
		  }
		});
		Object.defineProperty(exports, "createDet", {
		  enumerable: true,
		  get: function () {
		    return _det.createDet;
		  }
		});
		Object.defineProperty(exports, "createDeuteronMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createDeuteronMass;
		  }
		});
		Object.defineProperty(exports, "createDiag", {
		  enumerable: true,
		  get: function () {
		    return _diag.createDiag;
		  }
		});
		Object.defineProperty(exports, "createDiff", {
		  enumerable: true,
		  get: function () {
		    return _diff.createDiff;
		  }
		});
		Object.defineProperty(exports, "createDiffTransform", {
		  enumerable: true,
		  get: function () {
		    return _diffTransform.createDiffTransform;
		  }
		});
		Object.defineProperty(exports, "createDistance", {
		  enumerable: true,
		  get: function () {
		    return _distance.createDistance;
		  }
		});
		Object.defineProperty(exports, "createDivide", {
		  enumerable: true,
		  get: function () {
		    return _divide.createDivide;
		  }
		});
		Object.defineProperty(exports, "createDivideScalar", {
		  enumerable: true,
		  get: function () {
		    return _divideScalar.createDivideScalar;
		  }
		});
		Object.defineProperty(exports, "createDot", {
		  enumerable: true,
		  get: function () {
		    return _dot.createDot;
		  }
		});
		Object.defineProperty(exports, "createDotDivide", {
		  enumerable: true,
		  get: function () {
		    return _dotDivide.createDotDivide;
		  }
		});
		Object.defineProperty(exports, "createDotMultiply", {
		  enumerable: true,
		  get: function () {
		    return _dotMultiply.createDotMultiply;
		  }
		});
		Object.defineProperty(exports, "createDotPow", {
		  enumerable: true,
		  get: function () {
		    return _dotPow.createDotPow;
		  }
		});
		Object.defineProperty(exports, "createE", {
		  enumerable: true,
		  get: function () {
		    return _constants.createE;
		  }
		});
		Object.defineProperty(exports, "createEfimovFactor", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createEfimovFactor;
		  }
		});
		Object.defineProperty(exports, "createEigs", {
		  enumerable: true,
		  get: function () {
		    return _eigs.createEigs;
		  }
		});
		Object.defineProperty(exports, "createElectricConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createElectricConstant;
		  }
		});
		Object.defineProperty(exports, "createElectronMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createElectronMass;
		  }
		});
		Object.defineProperty(exports, "createElementaryCharge", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createElementaryCharge;
		  }
		});
		Object.defineProperty(exports, "createEqual", {
		  enumerable: true,
		  get: function () {
		    return _equal.createEqual;
		  }
		});
		Object.defineProperty(exports, "createEqualScalar", {
		  enumerable: true,
		  get: function () {
		    return _equalScalar.createEqualScalar;
		  }
		});
		Object.defineProperty(exports, "createEqualText", {
		  enumerable: true,
		  get: function () {
		    return _equalText.createEqualText;
		  }
		});
		Object.defineProperty(exports, "createErf", {
		  enumerable: true,
		  get: function () {
		    return _erf.createErf;
		  }
		});
		Object.defineProperty(exports, "createEvaluate", {
		  enumerable: true,
		  get: function () {
		    return _evaluate.createEvaluate;
		  }
		});
		Object.defineProperty(exports, "createExp", {
		  enumerable: true,
		  get: function () {
		    return _exp.createExp;
		  }
		});
		Object.defineProperty(exports, "createExpm", {
		  enumerable: true,
		  get: function () {
		    return _expm2.createExpm;
		  }
		});
		Object.defineProperty(exports, "createExpm1", {
		  enumerable: true,
		  get: function () {
		    return _expm.createExpm1;
		  }
		});
		Object.defineProperty(exports, "createFactorial", {
		  enumerable: true,
		  get: function () {
		    return _factorial.createFactorial;
		  }
		});
		Object.defineProperty(exports, "createFalse", {
		  enumerable: true,
		  get: function () {
		    return _constants.createFalse;
		  }
		});
		Object.defineProperty(exports, "createFaraday", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createFaraday;
		  }
		});
		Object.defineProperty(exports, "createFermiCoupling", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createFermiCoupling;
		  }
		});
		Object.defineProperty(exports, "createFft", {
		  enumerable: true,
		  get: function () {
		    return _fft.createFft;
		  }
		});
		Object.defineProperty(exports, "createFibonacciHeapClass", {
		  enumerable: true,
		  get: function () {
		    return _FibonacciHeap.createFibonacciHeapClass;
		  }
		});
		Object.defineProperty(exports, "createFilter", {
		  enumerable: true,
		  get: function () {
		    return _filter.createFilter;
		  }
		});
		Object.defineProperty(exports, "createFilterTransform", {
		  enumerable: true,
		  get: function () {
		    return _filterTransform.createFilterTransform;
		  }
		});
		Object.defineProperty(exports, "createFineStructure", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createFineStructure;
		  }
		});
		Object.defineProperty(exports, "createFirstRadiation", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createFirstRadiation;
		  }
		});
		Object.defineProperty(exports, "createFix", {
		  enumerable: true,
		  get: function () {
		    return _fix.createFix;
		  }
		});
		Object.defineProperty(exports, "createFlatten", {
		  enumerable: true,
		  get: function () {
		    return _flatten.createFlatten;
		  }
		});
		Object.defineProperty(exports, "createFloor", {
		  enumerable: true,
		  get: function () {
		    return _floor.createFloor;
		  }
		});
		Object.defineProperty(exports, "createForEach", {
		  enumerable: true,
		  get: function () {
		    return _forEach.createForEach;
		  }
		});
		Object.defineProperty(exports, "createForEachTransform", {
		  enumerable: true,
		  get: function () {
		    return _forEachTransform.createForEachTransform;
		  }
		});
		Object.defineProperty(exports, "createFormat", {
		  enumerable: true,
		  get: function () {
		    return _format.createFormat;
		  }
		});
		Object.defineProperty(exports, "createFraction", {
		  enumerable: true,
		  get: function () {
		    return _fraction.createFraction;
		  }
		});
		Object.defineProperty(exports, "createFractionClass", {
		  enumerable: true,
		  get: function () {
		    return _Fraction.createFractionClass;
		  }
		});
		Object.defineProperty(exports, "createFreqz", {
		  enumerable: true,
		  get: function () {
		    return _freqz.createFreqz;
		  }
		});
		Object.defineProperty(exports, "createFunctionAssignmentNode", {
		  enumerable: true,
		  get: function () {
		    return _FunctionAssignmentNode.createFunctionAssignmentNode;
		  }
		});
		Object.defineProperty(exports, "createFunctionNode", {
		  enumerable: true,
		  get: function () {
		    return _FunctionNode.createFunctionNode;
		  }
		});
		Object.defineProperty(exports, "createGamma", {
		  enumerable: true,
		  get: function () {
		    return _gamma.createGamma;
		  }
		});
		Object.defineProperty(exports, "createGasConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createGasConstant;
		  }
		});
		Object.defineProperty(exports, "createGcd", {
		  enumerable: true,
		  get: function () {
		    return _gcd.createGcd;
		  }
		});
		Object.defineProperty(exports, "createGetMatrixDataType", {
		  enumerable: true,
		  get: function () {
		    return _getMatrixDataType.createGetMatrixDataType;
		  }
		});
		Object.defineProperty(exports, "createGravitationConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createGravitationConstant;
		  }
		});
		Object.defineProperty(exports, "createGravity", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createGravity;
		  }
		});
		Object.defineProperty(exports, "createHartreeEnergy", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createHartreeEnergy;
		  }
		});
		Object.defineProperty(exports, "createHasNumericValue", {
		  enumerable: true,
		  get: function () {
		    return _hasNumericValue.createHasNumericValue;
		  }
		});
		Object.defineProperty(exports, "createHelp", {
		  enumerable: true,
		  get: function () {
		    return _help.createHelp;
		  }
		});
		Object.defineProperty(exports, "createHelpClass", {
		  enumerable: true,
		  get: function () {
		    return _Help.createHelpClass;
		  }
		});
		Object.defineProperty(exports, "createHex", {
		  enumerable: true,
		  get: function () {
		    return _hex.createHex;
		  }
		});
		Object.defineProperty(exports, "createHypot", {
		  enumerable: true,
		  get: function () {
		    return _hypot.createHypot;
		  }
		});
		Object.defineProperty(exports, "createI", {
		  enumerable: true,
		  get: function () {
		    return _constants.createI;
		  }
		});
		Object.defineProperty(exports, "createIdentity", {
		  enumerable: true,
		  get: function () {
		    return _identity.createIdentity;
		  }
		});
		Object.defineProperty(exports, "createIfft", {
		  enumerable: true,
		  get: function () {
		    return _ifft.createIfft;
		  }
		});
		Object.defineProperty(exports, "createIm", {
		  enumerable: true,
		  get: function () {
		    return _im.createIm;
		  }
		});
		Object.defineProperty(exports, "createImmutableDenseMatrixClass", {
		  enumerable: true,
		  get: function () {
		    return _ImmutableDenseMatrix.createImmutableDenseMatrixClass;
		  }
		});
		Object.defineProperty(exports, "createIndex", {
		  enumerable: true,
		  get: function () {
		    return _index.createIndex;
		  }
		});
		Object.defineProperty(exports, "createIndexClass", {
		  enumerable: true,
		  get: function () {
		    return _MatrixIndex.createIndexClass;
		  }
		});
		Object.defineProperty(exports, "createIndexNode", {
		  enumerable: true,
		  get: function () {
		    return _IndexNode.createIndexNode;
		  }
		});
		Object.defineProperty(exports, "createIndexTransform", {
		  enumerable: true,
		  get: function () {
		    return _indexTransform.createIndexTransform;
		  }
		});
		Object.defineProperty(exports, "createInfinity", {
		  enumerable: true,
		  get: function () {
		    return _constants.createInfinity;
		  }
		});
		Object.defineProperty(exports, "createIntersect", {
		  enumerable: true,
		  get: function () {
		    return _intersect.createIntersect;
		  }
		});
		Object.defineProperty(exports, "createInv", {
		  enumerable: true,
		  get: function () {
		    return _inv.createInv;
		  }
		});
		Object.defineProperty(exports, "createInverseConductanceQuantum", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createInverseConductanceQuantum;
		  }
		});
		Object.defineProperty(exports, "createInvmod", {
		  enumerable: true,
		  get: function () {
		    return _invmod.createInvmod;
		  }
		});
		Object.defineProperty(exports, "createIsBounded", {
		  enumerable: true,
		  get: function () {
		    return _isBounded.createIsBounded;
		  }
		});
		Object.defineProperty(exports, "createIsFinite", {
		  enumerable: true,
		  get: function () {
		    return _isFinite.createIsFinite;
		  }
		});
		Object.defineProperty(exports, "createIsInteger", {
		  enumerable: true,
		  get: function () {
		    return _isInteger.createIsInteger;
		  }
		});
		Object.defineProperty(exports, "createIsNaN", {
		  enumerable: true,
		  get: function () {
		    return _isNaN.createIsNaN;
		  }
		});
		Object.defineProperty(exports, "createIsNegative", {
		  enumerable: true,
		  get: function () {
		    return _isNegative.createIsNegative;
		  }
		});
		Object.defineProperty(exports, "createIsNumeric", {
		  enumerable: true,
		  get: function () {
		    return _isNumeric.createIsNumeric;
		  }
		});
		Object.defineProperty(exports, "createIsPositive", {
		  enumerable: true,
		  get: function () {
		    return _isPositive.createIsPositive;
		  }
		});
		Object.defineProperty(exports, "createIsPrime", {
		  enumerable: true,
		  get: function () {
		    return _isPrime.createIsPrime;
		  }
		});
		Object.defineProperty(exports, "createIsZero", {
		  enumerable: true,
		  get: function () {
		    return _isZero.createIsZero;
		  }
		});
		Object.defineProperty(exports, "createKldivergence", {
		  enumerable: true,
		  get: function () {
		    return _kldivergence.createKldivergence;
		  }
		});
		Object.defineProperty(exports, "createKlitzing", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createKlitzing;
		  }
		});
		Object.defineProperty(exports, "createKron", {
		  enumerable: true,
		  get: function () {
		    return _kron.createKron;
		  }
		});
		Object.defineProperty(exports, "createLN10", {
		  enumerable: true,
		  get: function () {
		    return _constants.createLN10;
		  }
		});
		Object.defineProperty(exports, "createLN2", {
		  enumerable: true,
		  get: function () {
		    return _constants.createLN2;
		  }
		});
		Object.defineProperty(exports, "createLOG10E", {
		  enumerable: true,
		  get: function () {
		    return _constants.createLOG10E;
		  }
		});
		Object.defineProperty(exports, "createLOG2E", {
		  enumerable: true,
		  get: function () {
		    return _constants.createLOG2E;
		  }
		});
		Object.defineProperty(exports, "createLarger", {
		  enumerable: true,
		  get: function () {
		    return _larger.createLarger;
		  }
		});
		Object.defineProperty(exports, "createLargerEq", {
		  enumerable: true,
		  get: function () {
		    return _largerEq.createLargerEq;
		  }
		});
		Object.defineProperty(exports, "createLcm", {
		  enumerable: true,
		  get: function () {
		    return _lcm.createLcm;
		  }
		});
		Object.defineProperty(exports, "createLeafCount", {
		  enumerable: true,
		  get: function () {
		    return _leafCount.createLeafCount;
		  }
		});
		Object.defineProperty(exports, "createLeftShift", {
		  enumerable: true,
		  get: function () {
		    return _leftShift.createLeftShift;
		  }
		});
		Object.defineProperty(exports, "createLgamma", {
		  enumerable: true,
		  get: function () {
		    return _lgamma.createLgamma;
		  }
		});
		Object.defineProperty(exports, "createLog", {
		  enumerable: true,
		  get: function () {
		    return _log3.createLog;
		  }
		});
		Object.defineProperty(exports, "createLog10", {
		  enumerable: true,
		  get: function () {
		    return _log.createLog10;
		  }
		});
		Object.defineProperty(exports, "createLog1p", {
		  enumerable: true,
		  get: function () {
		    return _log1p.createLog1p;
		  }
		});
		Object.defineProperty(exports, "createLog2", {
		  enumerable: true,
		  get: function () {
		    return _log2.createLog2;
		  }
		});
		Object.defineProperty(exports, "createLoschmidt", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createLoschmidt;
		  }
		});
		Object.defineProperty(exports, "createLsolve", {
		  enumerable: true,
		  get: function () {
		    return _lsolve.createLsolve;
		  }
		});
		Object.defineProperty(exports, "createLsolveAll", {
		  enumerable: true,
		  get: function () {
		    return _lsolveAll.createLsolveAll;
		  }
		});
		Object.defineProperty(exports, "createLup", {
		  enumerable: true,
		  get: function () {
		    return _lup.createLup;
		  }
		});
		Object.defineProperty(exports, "createLusolve", {
		  enumerable: true,
		  get: function () {
		    return _lusolve.createLusolve;
		  }
		});
		Object.defineProperty(exports, "createLyap", {
		  enumerable: true,
		  get: function () {
		    return _lyap.createLyap;
		  }
		});
		Object.defineProperty(exports, "createMad", {
		  enumerable: true,
		  get: function () {
		    return _mad.createMad;
		  }
		});
		Object.defineProperty(exports, "createMagneticConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createMagneticConstant;
		  }
		});
		Object.defineProperty(exports, "createMagneticFluxQuantum", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createMagneticFluxQuantum;
		  }
		});
		Object.defineProperty(exports, "createMap", {
		  enumerable: true,
		  get: function () {
		    return _map.createMap;
		  }
		});
		Object.defineProperty(exports, "createMapSlices", {
		  enumerable: true,
		  get: function () {
		    return _mapSlices.createMapSlices;
		  }
		});
		Object.defineProperty(exports, "createMapSlicesTransform", {
		  enumerable: true,
		  get: function () {
		    return _mapSlicesTransform.createMapSlicesTransform;
		  }
		});
		Object.defineProperty(exports, "createMapTransform", {
		  enumerable: true,
		  get: function () {
		    return _mapTransform.createMapTransform;
		  }
		});
		Object.defineProperty(exports, "createMatrix", {
		  enumerable: true,
		  get: function () {
		    return _matrix.createMatrix;
		  }
		});
		Object.defineProperty(exports, "createMatrixClass", {
		  enumerable: true,
		  get: function () {
		    return _Matrix.createMatrixClass;
		  }
		});
		Object.defineProperty(exports, "createMatrixFromColumns", {
		  enumerable: true,
		  get: function () {
		    return _matrixFromColumns.createMatrixFromColumns;
		  }
		});
		Object.defineProperty(exports, "createMatrixFromFunction", {
		  enumerable: true,
		  get: function () {
		    return _matrixFromFunction.createMatrixFromFunction;
		  }
		});
		Object.defineProperty(exports, "createMatrixFromRows", {
		  enumerable: true,
		  get: function () {
		    return _matrixFromRows.createMatrixFromRows;
		  }
		});
		Object.defineProperty(exports, "createMax", {
		  enumerable: true,
		  get: function () {
		    return _max.createMax;
		  }
		});
		Object.defineProperty(exports, "createMaxTransform", {
		  enumerable: true,
		  get: function () {
		    return _maxTransform.createMaxTransform;
		  }
		});
		Object.defineProperty(exports, "createMean", {
		  enumerable: true,
		  get: function () {
		    return _mean.createMean;
		  }
		});
		Object.defineProperty(exports, "createMeanTransform", {
		  enumerable: true,
		  get: function () {
		    return _meanTransform.createMeanTransform;
		  }
		});
		Object.defineProperty(exports, "createMedian", {
		  enumerable: true,
		  get: function () {
		    return _median.createMedian;
		  }
		});
		Object.defineProperty(exports, "createMin", {
		  enumerable: true,
		  get: function () {
		    return _min.createMin;
		  }
		});
		Object.defineProperty(exports, "createMinTransform", {
		  enumerable: true,
		  get: function () {
		    return _minTransform.createMinTransform;
		  }
		});
		Object.defineProperty(exports, "createMod", {
		  enumerable: true,
		  get: function () {
		    return _mod.createMod;
		  }
		});
		Object.defineProperty(exports, "createMode", {
		  enumerable: true,
		  get: function () {
		    return _mode.createMode;
		  }
		});
		Object.defineProperty(exports, "createMolarMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createMolarMass;
		  }
		});
		Object.defineProperty(exports, "createMolarMassC12", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createMolarMassC12;
		  }
		});
		Object.defineProperty(exports, "createMolarPlanckConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createMolarPlanckConstant;
		  }
		});
		Object.defineProperty(exports, "createMolarVolume", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createMolarVolume;
		  }
		});
		Object.defineProperty(exports, "createMultinomial", {
		  enumerable: true,
		  get: function () {
		    return _multinomial.createMultinomial;
		  }
		});
		Object.defineProperty(exports, "createMultiply", {
		  enumerable: true,
		  get: function () {
		    return _multiply.createMultiply;
		  }
		});
		Object.defineProperty(exports, "createMultiplyScalar", {
		  enumerable: true,
		  get: function () {
		    return _multiplyScalar.createMultiplyScalar;
		  }
		});
		Object.defineProperty(exports, "createNaN", {
		  enumerable: true,
		  get: function () {
		    return _constants.createNaN;
		  }
		});
		Object.defineProperty(exports, "createNeutronMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createNeutronMass;
		  }
		});
		Object.defineProperty(exports, "createNode", {
		  enumerable: true,
		  get: function () {
		    return _Node.createNode;
		  }
		});
		Object.defineProperty(exports, "createNorm", {
		  enumerable: true,
		  get: function () {
		    return _norm.createNorm;
		  }
		});
		Object.defineProperty(exports, "createNot", {
		  enumerable: true,
		  get: function () {
		    return _not.createNot;
		  }
		});
		Object.defineProperty(exports, "createNthRoot", {
		  enumerable: true,
		  get: function () {
		    return _nthRoot.createNthRoot;
		  }
		});
		Object.defineProperty(exports, "createNthRoots", {
		  enumerable: true,
		  get: function () {
		    return _nthRoots.createNthRoots;
		  }
		});
		Object.defineProperty(exports, "createNuclearMagneton", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createNuclearMagneton;
		  }
		});
		Object.defineProperty(exports, "createNull", {
		  enumerable: true,
		  get: function () {
		    return _constants.createNull;
		  }
		});
		Object.defineProperty(exports, "createNullish", {
		  enumerable: true,
		  get: function () {
		    return _nullish.createNullish;
		  }
		});
		Object.defineProperty(exports, "createNullishTransform", {
		  enumerable: true,
		  get: function () {
		    return _nullishTransform.createNullishTransform;
		  }
		});
		Object.defineProperty(exports, "createNum", {
		  enumerable: true,
		  get: function () {
		    return _num.createNum;
		  }
		});
		Object.defineProperty(exports, "createNumber", {
		  enumerable: true,
		  get: function () {
		    return _number.createNumber;
		  }
		});
		Object.defineProperty(exports, "createNumeric", {
		  enumerable: true,
		  get: function () {
		    return _numeric.createNumeric;
		  }
		});
		Object.defineProperty(exports, "createObjectNode", {
		  enumerable: true,
		  get: function () {
		    return _ObjectNode.createObjectNode;
		  }
		});
		Object.defineProperty(exports, "createOct", {
		  enumerable: true,
		  get: function () {
		    return _oct.createOct;
		  }
		});
		Object.defineProperty(exports, "createOnes", {
		  enumerable: true,
		  get: function () {
		    return _ones.createOnes;
		  }
		});
		Object.defineProperty(exports, "createOperatorNode", {
		  enumerable: true,
		  get: function () {
		    return _OperatorNode.createOperatorNode;
		  }
		});
		Object.defineProperty(exports, "createOr", {
		  enumerable: true,
		  get: function () {
		    return _or.createOr;
		  }
		});
		Object.defineProperty(exports, "createOrTransform", {
		  enumerable: true,
		  get: function () {
		    return _orTransform.createOrTransform;
		  }
		});
		Object.defineProperty(exports, "createParenthesisNode", {
		  enumerable: true,
		  get: function () {
		    return _ParenthesisNode.createParenthesisNode;
		  }
		});
		Object.defineProperty(exports, "createParse", {
		  enumerable: true,
		  get: function () {
		    return _parse.createParse;
		  }
		});
		Object.defineProperty(exports, "createParser", {
		  enumerable: true,
		  get: function () {
		    return _parser.createParser;
		  }
		});
		Object.defineProperty(exports, "createParserClass", {
		  enumerable: true,
		  get: function () {
		    return _Parser.createParserClass;
		  }
		});
		Object.defineProperty(exports, "createPartitionSelect", {
		  enumerable: true,
		  get: function () {
		    return _partitionSelect.createPartitionSelect;
		  }
		});
		Object.defineProperty(exports, "createPermutations", {
		  enumerable: true,
		  get: function () {
		    return _permutations.createPermutations;
		  }
		});
		Object.defineProperty(exports, "createPhi", {
		  enumerable: true,
		  get: function () {
		    return _constants.createPhi;
		  }
		});
		Object.defineProperty(exports, "createPi", {
		  enumerable: true,
		  get: function () {
		    return _constants.createPi;
		  }
		});
		Object.defineProperty(exports, "createPickRandom", {
		  enumerable: true,
		  get: function () {
		    return _pickRandom.createPickRandom;
		  }
		});
		Object.defineProperty(exports, "createPinv", {
		  enumerable: true,
		  get: function () {
		    return _pinv.createPinv;
		  }
		});
		Object.defineProperty(exports, "createPlanckCharge", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createPlanckCharge;
		  }
		});
		Object.defineProperty(exports, "createPlanckConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createPlanckConstant;
		  }
		});
		Object.defineProperty(exports, "createPlanckLength", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createPlanckLength;
		  }
		});
		Object.defineProperty(exports, "createPlanckMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createPlanckMass;
		  }
		});
		Object.defineProperty(exports, "createPlanckTemperature", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createPlanckTemperature;
		  }
		});
		Object.defineProperty(exports, "createPlanckTime", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createPlanckTime;
		  }
		});
		Object.defineProperty(exports, "createPolynomialRoot", {
		  enumerable: true,
		  get: function () {
		    return _polynomialRoot.createPolynomialRoot;
		  }
		});
		Object.defineProperty(exports, "createPow", {
		  enumerable: true,
		  get: function () {
		    return _pow.createPow;
		  }
		});
		Object.defineProperty(exports, "createPrint", {
		  enumerable: true,
		  get: function () {
		    return _print.createPrint;
		  }
		});
		Object.defineProperty(exports, "createPrintTransform", {
		  enumerable: true,
		  get: function () {
		    return _printTransform.createPrintTransform;
		  }
		});
		Object.defineProperty(exports, "createProd", {
		  enumerable: true,
		  get: function () {
		    return _prod.createProd;
		  }
		});
		Object.defineProperty(exports, "createProtonMass", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createProtonMass;
		  }
		});
		Object.defineProperty(exports, "createQr", {
		  enumerable: true,
		  get: function () {
		    return _qr.createQr;
		  }
		});
		Object.defineProperty(exports, "createQuantileSeq", {
		  enumerable: true,
		  get: function () {
		    return _quantileSeq.createQuantileSeq;
		  }
		});
		Object.defineProperty(exports, "createQuantileSeqTransform", {
		  enumerable: true,
		  get: function () {
		    return _quantileSeqTransform.createQuantileSeqTransform;
		  }
		});
		Object.defineProperty(exports, "createQuantumOfCirculation", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createQuantumOfCirculation;
		  }
		});
		Object.defineProperty(exports, "createRandom", {
		  enumerable: true,
		  get: function () {
		    return _random.createRandom;
		  }
		});
		Object.defineProperty(exports, "createRandomInt", {
		  enumerable: true,
		  get: function () {
		    return _randomInt.createRandomInt;
		  }
		});
		Object.defineProperty(exports, "createRange", {
		  enumerable: true,
		  get: function () {
		    return _range.createRange;
		  }
		});
		Object.defineProperty(exports, "createRangeClass", {
		  enumerable: true,
		  get: function () {
		    return _Range.createRangeClass;
		  }
		});
		Object.defineProperty(exports, "createRangeNode", {
		  enumerable: true,
		  get: function () {
		    return _RangeNode.createRangeNode;
		  }
		});
		Object.defineProperty(exports, "createRangeTransform", {
		  enumerable: true,
		  get: function () {
		    return _rangeTransform.createRangeTransform;
		  }
		});
		Object.defineProperty(exports, "createRationalize", {
		  enumerable: true,
		  get: function () {
		    return _rationalize.createRationalize;
		  }
		});
		Object.defineProperty(exports, "createRe", {
		  enumerable: true,
		  get: function () {
		    return _re.createRe;
		  }
		});
		Object.defineProperty(exports, "createReducedPlanckConstant", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createReducedPlanckConstant;
		  }
		});
		Object.defineProperty(exports, "createRelationalNode", {
		  enumerable: true,
		  get: function () {
		    return _RelationalNode.createRelationalNode;
		  }
		});
		Object.defineProperty(exports, "createReplacer", {
		  enumerable: true,
		  get: function () {
		    return _replacer.createReplacer;
		  }
		});
		Object.defineProperty(exports, "createReshape", {
		  enumerable: true,
		  get: function () {
		    return _reshape.createReshape;
		  }
		});
		Object.defineProperty(exports, "createResize", {
		  enumerable: true,
		  get: function () {
		    return _resize.createResize;
		  }
		});
		Object.defineProperty(exports, "createResolve", {
		  enumerable: true,
		  get: function () {
		    return _resolve.createResolve;
		  }
		});
		Object.defineProperty(exports, "createResultSet", {
		  enumerable: true,
		  get: function () {
		    return _ResultSet.createResultSet;
		  }
		});
		Object.defineProperty(exports, "createReviver", {
		  enumerable: true,
		  get: function () {
		    return _reviver.createReviver;
		  }
		});
		Object.defineProperty(exports, "createRightArithShift", {
		  enumerable: true,
		  get: function () {
		    return _rightArithShift.createRightArithShift;
		  }
		});
		Object.defineProperty(exports, "createRightLogShift", {
		  enumerable: true,
		  get: function () {
		    return _rightLogShift.createRightLogShift;
		  }
		});
		Object.defineProperty(exports, "createRotate", {
		  enumerable: true,
		  get: function () {
		    return _rotate.createRotate;
		  }
		});
		Object.defineProperty(exports, "createRotationMatrix", {
		  enumerable: true,
		  get: function () {
		    return _rotationMatrix.createRotationMatrix;
		  }
		});
		Object.defineProperty(exports, "createRound", {
		  enumerable: true,
		  get: function () {
		    return _round.createRound;
		  }
		});
		Object.defineProperty(exports, "createRow", {
		  enumerable: true,
		  get: function () {
		    return _row.createRow;
		  }
		});
		Object.defineProperty(exports, "createRowTransform", {
		  enumerable: true,
		  get: function () {
		    return _rowTransform.createRowTransform;
		  }
		});
		Object.defineProperty(exports, "createRydberg", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createRydberg;
		  }
		});
		Object.defineProperty(exports, "createSQRT1_2", {
		  enumerable: true,
		  get: function () {
		    return _constants.createSQRT1_2;
		  }
		});
		Object.defineProperty(exports, "createSQRT2", {
		  enumerable: true,
		  get: function () {
		    return _constants.createSQRT2;
		  }
		});
		Object.defineProperty(exports, "createSackurTetrode", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createSackurTetrode;
		  }
		});
		Object.defineProperty(exports, "createSchur", {
		  enumerable: true,
		  get: function () {
		    return _schur.createSchur;
		  }
		});
		Object.defineProperty(exports, "createSec", {
		  enumerable: true,
		  get: function () {
		    return _sec.createSec;
		  }
		});
		Object.defineProperty(exports, "createSech", {
		  enumerable: true,
		  get: function () {
		    return _sech.createSech;
		  }
		});
		Object.defineProperty(exports, "createSecondRadiation", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createSecondRadiation;
		  }
		});
		Object.defineProperty(exports, "createSetCartesian", {
		  enumerable: true,
		  get: function () {
		    return _setCartesian.createSetCartesian;
		  }
		});
		Object.defineProperty(exports, "createSetDifference", {
		  enumerable: true,
		  get: function () {
		    return _setDifference.createSetDifference;
		  }
		});
		Object.defineProperty(exports, "createSetDistinct", {
		  enumerable: true,
		  get: function () {
		    return _setDistinct.createSetDistinct;
		  }
		});
		Object.defineProperty(exports, "createSetIntersect", {
		  enumerable: true,
		  get: function () {
		    return _setIntersect.createSetIntersect;
		  }
		});
		Object.defineProperty(exports, "createSetIsSubset", {
		  enumerable: true,
		  get: function () {
		    return _setIsSubset.createSetIsSubset;
		  }
		});
		Object.defineProperty(exports, "createSetMultiplicity", {
		  enumerable: true,
		  get: function () {
		    return _setMultiplicity.createSetMultiplicity;
		  }
		});
		Object.defineProperty(exports, "createSetPowerset", {
		  enumerable: true,
		  get: function () {
		    return _setPowerset.createSetPowerset;
		  }
		});
		Object.defineProperty(exports, "createSetSize", {
		  enumerable: true,
		  get: function () {
		    return _setSize.createSetSize;
		  }
		});
		Object.defineProperty(exports, "createSetSymDifference", {
		  enumerable: true,
		  get: function () {
		    return _setSymDifference.createSetSymDifference;
		  }
		});
		Object.defineProperty(exports, "createSetUnion", {
		  enumerable: true,
		  get: function () {
		    return _setUnion.createSetUnion;
		  }
		});
		Object.defineProperty(exports, "createSign", {
		  enumerable: true,
		  get: function () {
		    return _sign.createSign;
		  }
		});
		Object.defineProperty(exports, "createSimplify", {
		  enumerable: true,
		  get: function () {
		    return _simplify.createSimplify;
		  }
		});
		Object.defineProperty(exports, "createSimplifyConstant", {
		  enumerable: true,
		  get: function () {
		    return _simplifyConstant.createSimplifyConstant;
		  }
		});
		Object.defineProperty(exports, "createSimplifyCore", {
		  enumerable: true,
		  get: function () {
		    return _simplifyCore.createSimplifyCore;
		  }
		});
		Object.defineProperty(exports, "createSin", {
		  enumerable: true,
		  get: function () {
		    return _sin.createSin;
		  }
		});
		Object.defineProperty(exports, "createSinh", {
		  enumerable: true,
		  get: function () {
		    return _sinh.createSinh;
		  }
		});
		Object.defineProperty(exports, "createSize", {
		  enumerable: true,
		  get: function () {
		    return _size.createSize;
		  }
		});
		Object.defineProperty(exports, "createSlu", {
		  enumerable: true,
		  get: function () {
		    return _slu.createSlu;
		  }
		});
		Object.defineProperty(exports, "createSmaller", {
		  enumerable: true,
		  get: function () {
		    return _smaller.createSmaller;
		  }
		});
		Object.defineProperty(exports, "createSmallerEq", {
		  enumerable: true,
		  get: function () {
		    return _smallerEq.createSmallerEq;
		  }
		});
		Object.defineProperty(exports, "createSolveODE", {
		  enumerable: true,
		  get: function () {
		    return _solveODE.createSolveODE;
		  }
		});
		Object.defineProperty(exports, "createSort", {
		  enumerable: true,
		  get: function () {
		    return _sort.createSort;
		  }
		});
		Object.defineProperty(exports, "createSpaClass", {
		  enumerable: true,
		  get: function () {
		    return _Spa.createSpaClass;
		  }
		});
		Object.defineProperty(exports, "createSparse", {
		  enumerable: true,
		  get: function () {
		    return _sparse.createSparse;
		  }
		});
		Object.defineProperty(exports, "createSparseMatrixClass", {
		  enumerable: true,
		  get: function () {
		    return _SparseMatrix.createSparseMatrixClass;
		  }
		});
		Object.defineProperty(exports, "createSpeedOfLight", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createSpeedOfLight;
		  }
		});
		Object.defineProperty(exports, "createSplitUnit", {
		  enumerable: true,
		  get: function () {
		    return _splitUnit.createSplitUnit;
		  }
		});
		Object.defineProperty(exports, "createSqrt", {
		  enumerable: true,
		  get: function () {
		    return _sqrt.createSqrt;
		  }
		});
		Object.defineProperty(exports, "createSqrtm", {
		  enumerable: true,
		  get: function () {
		    return _sqrtm.createSqrtm;
		  }
		});
		Object.defineProperty(exports, "createSquare", {
		  enumerable: true,
		  get: function () {
		    return _square.createSquare;
		  }
		});
		Object.defineProperty(exports, "createSqueeze", {
		  enumerable: true,
		  get: function () {
		    return _squeeze.createSqueeze;
		  }
		});
		Object.defineProperty(exports, "createStd", {
		  enumerable: true,
		  get: function () {
		    return _std.createStd;
		  }
		});
		Object.defineProperty(exports, "createStdTransform", {
		  enumerable: true,
		  get: function () {
		    return _stdTransform.createStdTransform;
		  }
		});
		Object.defineProperty(exports, "createStefanBoltzmann", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createStefanBoltzmann;
		  }
		});
		Object.defineProperty(exports, "createStirlingS2", {
		  enumerable: true,
		  get: function () {
		    return _stirlingS.createStirlingS2;
		  }
		});
		Object.defineProperty(exports, "createString", {
		  enumerable: true,
		  get: function () {
		    return _string.createString;
		  }
		});
		Object.defineProperty(exports, "createSubset", {
		  enumerable: true,
		  get: function () {
		    return _subset.createSubset;
		  }
		});
		Object.defineProperty(exports, "createSubsetTransform", {
		  enumerable: true,
		  get: function () {
		    return _subsetTransform.createSubsetTransform;
		  }
		});
		Object.defineProperty(exports, "createSubtract", {
		  enumerable: true,
		  get: function () {
		    return _subtract.createSubtract;
		  }
		});
		Object.defineProperty(exports, "createSubtractScalar", {
		  enumerable: true,
		  get: function () {
		    return _subtractScalar.createSubtractScalar;
		  }
		});
		Object.defineProperty(exports, "createSum", {
		  enumerable: true,
		  get: function () {
		    return _sum.createSum;
		  }
		});
		Object.defineProperty(exports, "createSumTransform", {
		  enumerable: true,
		  get: function () {
		    return _sumTransform.createSumTransform;
		  }
		});
		Object.defineProperty(exports, "createSylvester", {
		  enumerable: true,
		  get: function () {
		    return _sylvester.createSylvester;
		  }
		});
		Object.defineProperty(exports, "createSymbolNode", {
		  enumerable: true,
		  get: function () {
		    return _SymbolNode.createSymbolNode;
		  }
		});
		Object.defineProperty(exports, "createSymbolicEqual", {
		  enumerable: true,
		  get: function () {
		    return _symbolicEqual.createSymbolicEqual;
		  }
		});
		Object.defineProperty(exports, "createTan", {
		  enumerable: true,
		  get: function () {
		    return _tan.createTan;
		  }
		});
		Object.defineProperty(exports, "createTanh", {
		  enumerable: true,
		  get: function () {
		    return _tanh.createTanh;
		  }
		});
		Object.defineProperty(exports, "createTau", {
		  enumerable: true,
		  get: function () {
		    return _constants.createTau;
		  }
		});
		Object.defineProperty(exports, "createThomsonCrossSection", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createThomsonCrossSection;
		  }
		});
		Object.defineProperty(exports, "createTo", {
		  enumerable: true,
		  get: function () {
		    return _to.createTo;
		  }
		});
		Object.defineProperty(exports, "createToBest", {
		  enumerable: true,
		  get: function () {
		    return _toBest.createToBest;
		  }
		});
		Object.defineProperty(exports, "createTrace", {
		  enumerable: true,
		  get: function () {
		    return _trace.createTrace;
		  }
		});
		Object.defineProperty(exports, "createTranspose", {
		  enumerable: true,
		  get: function () {
		    return _transpose.createTranspose;
		  }
		});
		Object.defineProperty(exports, "createTrue", {
		  enumerable: true,
		  get: function () {
		    return _constants.createTrue;
		  }
		});
		Object.defineProperty(exports, "createTypeOf", {
		  enumerable: true,
		  get: function () {
		    return _typeOf.createTypeOf;
		  }
		});
		Object.defineProperty(exports, "createTyped", {
		  enumerable: true,
		  get: function () {
		    return _typed.createTyped;
		  }
		});
		Object.defineProperty(exports, "createUnaryMinus", {
		  enumerable: true,
		  get: function () {
		    return _unaryMinus.createUnaryMinus;
		  }
		});
		Object.defineProperty(exports, "createUnaryPlus", {
		  enumerable: true,
		  get: function () {
		    return _unaryPlus.createUnaryPlus;
		  }
		});
		Object.defineProperty(exports, "createUnequal", {
		  enumerable: true,
		  get: function () {
		    return _unequal.createUnequal;
		  }
		});
		Object.defineProperty(exports, "createUnitClass", {
		  enumerable: true,
		  get: function () {
		    return _Unit.createUnitClass;
		  }
		});
		Object.defineProperty(exports, "createUnitFunction", {
		  enumerable: true,
		  get: function () {
		    return _unit.createUnitFunction;
		  }
		});
		Object.defineProperty(exports, "createUppercaseE", {
		  enumerable: true,
		  get: function () {
		    return _constants.createUppercaseE;
		  }
		});
		Object.defineProperty(exports, "createUppercasePi", {
		  enumerable: true,
		  get: function () {
		    return _constants.createUppercasePi;
		  }
		});
		Object.defineProperty(exports, "createUsolve", {
		  enumerable: true,
		  get: function () {
		    return _usolve.createUsolve;
		  }
		});
		Object.defineProperty(exports, "createUsolveAll", {
		  enumerable: true,
		  get: function () {
		    return _usolveAll.createUsolveAll;
		  }
		});
		Object.defineProperty(exports, "createVacuumImpedance", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createVacuumImpedance;
		  }
		});
		Object.defineProperty(exports, "createVariance", {
		  enumerable: true,
		  get: function () {
		    return _variance.createVariance;
		  }
		});
		Object.defineProperty(exports, "createVarianceTransform", {
		  enumerable: true,
		  get: function () {
		    return _varianceTransform.createVarianceTransform;
		  }
		});
		Object.defineProperty(exports, "createVersion", {
		  enumerable: true,
		  get: function () {
		    return _constants.createVersion;
		  }
		});
		Object.defineProperty(exports, "createWeakMixingAngle", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createWeakMixingAngle;
		  }
		});
		Object.defineProperty(exports, "createWienDisplacement", {
		  enumerable: true,
		  get: function () {
		    return _physicalConstants.createWienDisplacement;
		  }
		});
		Object.defineProperty(exports, "createXgcd", {
		  enumerable: true,
		  get: function () {
		    return _xgcd.createXgcd;
		  }
		});
		Object.defineProperty(exports, "createXor", {
		  enumerable: true,
		  get: function () {
		    return _xor.createXor;
		  }
		});
		Object.defineProperty(exports, "createZeros", {
		  enumerable: true,
		  get: function () {
		    return _zeros.createZeros;
		  }
		});
		Object.defineProperty(exports, "createZeta", {
		  enumerable: true,
		  get: function () {
		    return _zeta.createZeta;
		  }
		});
		Object.defineProperty(exports, "createZpk2tf", {
		  enumerable: true,
		  get: function () {
		    return _zpk2tf.createZpk2tf;
		  }
		});
		var _typed = requireTyped$1();
		var _ResultSet = requireResultSet();
		var _BigNumber = requireBigNumber();
		var _Complex = requireComplex$3();
		var _Fraction = requireFraction$2();
		var _Range = requireRange$2();
		var _Matrix = requireMatrix$2();
		var _DenseMatrix = requireDenseMatrix();
		var _clone = requireClone$1();
		var _isInteger = requireIsInteger$1();
		var _isNegative = requireIsNegative$1();
		var _isNumeric = requireIsNumeric$1();
		var _hasNumericValue = requireHasNumericValue$1();
		var _isPositive = requireIsPositive$1();
		var _isZero = requireIsZero$1();
		var _isNaN = require_isNaN$1();
		var _isBounded = requireIsBounded$1();
		var _isFinite = require_isFinite$1();
		var _typeOf = requireTypeOf$1();
		var _equalScalar = requireEqualScalar();
		var _SparseMatrix = requireSparseMatrix();
		var _number = requireNumber$1();
		var _bigint = requireBigint$2();
		var _string = requireString$1();
		var _boolean = requireBoolean$1();
		var _bignumber = requireBignumber$1();
		var _complex = requireComplex$1();
		var _fraction = requireFraction$1();
		var _matrix = requireMatrix$1();
		var _matrixFromFunction = requireMatrixFromFunction$1();
		var _matrixFromRows = requireMatrixFromRows$1();
		var _matrixFromColumns = requireMatrixFromColumns$1();
		var _splitUnit = requireSplitUnit$1();
		var _unaryMinus = requireUnaryMinus$1();
		var _unaryPlus = requireUnaryPlus$1();
		var _abs = requireAbs$1();
		var _mapSlices = requireMapSlices$1();
		var _addScalar = requireAddScalar();
		var _subtractScalar = requireSubtractScalar();
		var _cbrt = requireCbrt$1();
		var _ceil = requireCeil$1();
		var _cube = requireCube$1();
		var _exp = requireExp$1();
		var _expm = requireExpm1$1();
		var _fix = requireFix$1();
		var _floor = requireFloor$1();
		var _gcd = requireGcd$1();
		var _lcm = requireLcm$1();
		var _log = requireLog10$1();
		var _log2 = requireLog2$1();
		var _mod = requireMod$1();
		var _multiplyScalar = requireMultiplyScalar();
		var _multiply = requireMultiply$1();
		var _nthRoot = requireNthRoot$1();
		var _sign = requireSign$1();
		var _sqrt = requireSqrt$1();
		var _square = requireSquare$1();
		var _subtract = requireSubtract$1();
		var _xgcd = requireXgcd$1();
		var _invmod = requireInvmod$1();
		var _dotMultiply = requireDotMultiply$1();
		var _bitAnd = requireBitAnd$1();
		var _bitNot = requireBitNot$1();
		var _bitOr = requireBitOr$1();
		var _bitXor = requireBitXor$1();
		var _arg = requireArg$1();
		var _conj = requireConj$1();
		var _im = requireIm$1();
		var _re = requireRe$1();
		var _not = requireNot$1();
		var _nullish = requireNullish$1();
		var _or = requireOr$1();
		var _xor = requireXor$1();
		var _concat = requireConcat$1();
		var _column = requireColumn$1();
		var _count = requireCount$1();
		var _cross = requireCross$1();
		var _diag = requireDiag$1();
		var _filter = requireFilter$1();
		var _flatten = requireFlatten$1();
		var _forEach = requireForEach$1();
		var _getMatrixDataType = requireGetMatrixDataType$1();
		var _identity = requireIdentity$1();
		var _kron = requireKron$1();
		var _map = requireMap$1();
		var _diff = requireDiff$1();
		var _ones = requireOnes$1();
		var _range = requireRange$1();
		var _reshape = requireReshape$1();
		var _resize = requireResize$1();
		var _rotate = requireRotate$1();
		var _rotationMatrix = requireRotationMatrix$1();
		var _row = requireRow$1();
		var _size = requireSize$1();
		var _squeeze = requireSqueeze$1();
		var _subset = requireSubset$1();
		var _transpose = requireTranspose$1();
		var _ctranspose = requireCtranspose$1();
		var _zeros = requireZeros$1();
		var _fft = requireFft$1();
		var _ifft = requireIfft$1();
		var _solveODE = requireSolveODE$1();
		var _erf = requireErf$1();
		var _zeta = requireZeta$1();
		var _mode = requireMode$1();
		var _prod = requireProd$1();
		var _format = requireFormat$1();
		var _bin = requireBin$1();
		var _oct = requireOct$1();
		var _hex = requireHex$1();
		var _print = requirePrint$1();
		var _to = requireTo$1();
		var _toBest = requireToBest$1();
		var _isPrime = requireIsPrime$1();
		var _numeric = requireNumeric$1();
		var _divideScalar = requireDivideScalar();
		var _pow = requirePow$1();
		var _round = requireRound$1();
		var _log3 = requireLog$1();
		var _log1p = requireLog1p$1();
		var _nthRoots = requireNthRoots$1();
		var _dotPow = requireDotPow$1();
		var _dotDivide = requireDotDivide$1();
		var _lsolve = requireLsolve$1();
		var _usolve = requireUsolve$1();
		var _lsolveAll = requireLsolveAll$1();
		var _usolveAll = requireUsolveAll$1();
		var _leftShift = requireLeftShift$1();
		var _rightArithShift = requireRightArithShift$1();
		var _rightLogShift = requireRightLogShift$1();
		var _and = requireAnd$1();
		var _compare = requireCompare$1();
		var _compareNatural = requireCompareNatural$1();
		var _compareText = requireCompareText$1();
		var _equal = requireEqual$1();
		var _equalText = requireEqualText$1();
		var _smaller = requireSmaller$1();
		var _smallerEq = requireSmallerEq$1();
		var _larger = requireLarger$1();
		var _largerEq = requireLargerEq$1();
		var _deepEqual = requireDeepEqual$1();
		var _unequal = requireUnequal$1();
		var _partitionSelect = requirePartitionSelect$1();
		var _sort = requireSort$1();
		var _max = requireMax$1();
		var _min = requireMin$1();
		var _ImmutableDenseMatrix = requireImmutableDenseMatrix();
		var _MatrixIndex = requireMatrixIndex();
		var _FibonacciHeap = requireFibonacciHeap();
		var _Spa = requireSpa();
		var _Unit = requireUnit$2();
		var _unit = requireUnit$1();
		var _sparse = requireSparse$1();
		var _createUnit = requireCreateUnit$1();
		var _acos = requireAcos$1();
		var _acosh = requireAcosh$1();
		var _acot = requireAcot$1();
		var _acoth = requireAcoth$1();
		var _acsc = requireAcsc$1();
		var _acsch = requireAcsch$1();
		var _asec = requireAsec$1();
		var _asech = requireAsech$1();
		var _asin = requireAsin$1();
		var _asinh = requireAsinh$1();
		var _atan = requireAtan$1();
		var _atan2 = requireAtan2$1();
		var _atanh = requireAtanh$1();
		var _cos = requireCos$1();
		var _cosh = requireCosh$1();
		var _cot = requireCot$1();
		var _coth = requireCoth$1();
		var _csc = requireCsc$1();
		var _csch = requireCsch$1();
		var _sec = requireSec$1();
		var _sech = requireSech$1();
		var _sin = requireSin$1();
		var _sinh = requireSinh$1();
		var _tan = requireTan$1();
		var _tanh = requireTanh$1();
		var _setCartesian = requireSetCartesian$1();
		var _setDifference = requireSetDifference$1();
		var _setDistinct = requireSetDistinct$1();
		var _setIntersect = requireSetIntersect$1();
		var _setIsSubset = requireSetIsSubset$1();
		var _setMultiplicity = requireSetMultiplicity$1();
		var _setPowerset = requireSetPowerset$1();
		var _setSize = requireSetSize$1();
		var _setSymDifference = requireSetSymDifference$1();
		var _setUnion = requireSetUnion$1();
		var _add = requireAdd$1();
		var _hypot = requireHypot$1();
		var _norm = requireNorm$1();
		var _dot = requireDot$1();
		var _trace = requireTrace$1();
		var _index = require_function();
		var _Node = requireNode();
		var _AccessorNode = requireAccessorNode();
		var _ArrayNode = requireArrayNode();
		var _AssignmentNode = requireAssignmentNode();
		var _BlockNode = requireBlockNode();
		var _ConditionalNode = requireConditionalNode();
		var _ConstantNode = requireConstantNode();
		var _FunctionAssignmentNode = requireFunctionAssignmentNode();
		var _IndexNode = requireIndexNode();
		var _ObjectNode = requireObjectNode();
		var _OperatorNode = requireOperatorNode();
		var _ParenthesisNode = requireParenthesisNode();
		var _RangeNode = requireRangeNode();
		var _RelationalNode = requireRelationalNode();
		var _SymbolNode = requireSymbolNode();
		var _FunctionNode = requireFunctionNode();
		var _parse = requireParse$2();
		var _compile = requireCompile$2();
		var _evaluate = requireEvaluate$1();
		var _Parser = requireParser$3();
		var _parser = requireParser$2();
		var _lup = requireLup$1();
		var _qr = requireQr$1();
		var _slu = requireSlu$1();
		var _lusolve = requireLusolve$1();
		var _polynomialRoot = requirePolynomialRoot$1();
		var _Help = requireHelp$2();
		var _Chain = requireChain$1();
		var _help = requireHelp();
		var _chain = requireChain();
		var _det = requireDet();
		var _inv = requireInv();
		var _pinv = requirePinv();
		var _eigs = requireEigs();
		var _expm2 = requireExpm();
		var _sqrtm = requireSqrtm();
		var _sylvester = requireSylvester();
		var _schur = requireSchur();
		var _lyap = requireLyap();
		var _divide = requireDivide();
		var _distance = requireDistance();
		var _intersect = requireIntersect();
		var _sum = requireSum();
		var _cumsum = requireCumsum();
		var _mean = requireMean();
		var _median = requireMedian();
		var _mad = requireMad();
		var _variance = requireVariance();
		var _quantileSeq = requireQuantileSeq();
		var _std = requireStd();
		var _corr = requireCorr();
		var _bernoulli = requireBernoulli();
		var _combinations = requireCombinations();
		var _combinationsWithRep = requireCombinationsWithRep();
		var _gamma = requireGamma();
		var _lgamma = requireLgamma();
		var _factorial = requireFactorial();
		var _kldivergence = requireKldivergence();
		var _multinomial = requireMultinomial();
		var _permutations = requirePermutations();
		var _pickRandom = requirePickRandom();
		var _random = requireRandom();
		var _randomInt = requireRandomInt();
		var _stirlingS = requireStirlingS2();
		var _bellNumbers = requireBellNumbers();
		var _catalan = requireCatalan();
		var _composition = requireComposition();
		var _leafCount = requireLeafCount();
		var _simplify = requireSimplify();
		var _simplifyConstant = requireSimplifyConstant();
		var _simplifyCore = requireSimplifyCore();
		var _resolve = requireResolve();
		var _symbolicEqual = requireSymbolicEqual();
		var _derivative = requireDerivative();
		var _rationalize = requireRationalize();
		var _zpk2tf = requireZpk2tf();
		var _freqz = requireFreqz();
		var _reviver = requireReviver();
		var _replacer = requireReplacer();
		var _constants = requireConstants$2();
		var _physicalConstants = requirePhysicalConstants();
		var _mapSlicesTransform = requireMapSlices_transform();
		var _columnTransform = requireColumn_transform();
		var _filterTransform = requireFilter_transform();
		var _forEachTransform = requireForEach_transform();
		var _indexTransform = requireIndex_transform();
		var _mapTransform = requireMap_transform();
		var _maxTransform = requireMax_transform();
		var _meanTransform = requireMean_transform();
		var _minTransform = requireMin_transform();
		var _rangeTransform = requireRange_transform();
		var _rowTransform = requireRow_transform();
		var _subsetTransform = requireSubset_transform();
		var _concatTransform = requireConcat_transform();
		var _diffTransform = requireDiff_transform();
		var _stdTransform = requireStd_transform();
		var _sumTransform = requireSum_transform();
		var _quantileSeqTransform = requireQuantileSeq_transform();
		var _cumsumTransform = requireCumsum_transform();
		var _varianceTransform = requireVariance_transform();
		var _printTransform = requirePrint_transform();
		var _andTransform = requireAnd_transform();
		var _orTransform = requireOr_transform();
		var _nullishTransform = requireNullish_transform();
		var _bitAndTransform = requireBitAnd_transform();
		var _bitOrTransform = requireBitOr_transform();
		var _num = requireNum();
		var _den = requireDen(); 
	} (factoriesAny));
	return factoriesAny;
}

var hasRequiredPureFunctionsAny_generated;

function requirePureFunctionsAny_generated () {
	if (hasRequiredPureFunctionsAny_generated) return pureFunctionsAny_generated;
	hasRequiredPureFunctionsAny_generated = 1;

	Object.defineProperty(pureFunctionsAny_generated, "__esModule", {
	  value: true
	});
	pureFunctionsAny_generated.dotDivide = pureFunctionsAny_generated.dot = pureFunctionsAny_generated.divideScalar = pureFunctionsAny_generated.divide = pureFunctionsAny_generated.distance = pureFunctionsAny_generated.diff = pureFunctionsAny_generated.diag = pureFunctionsAny_generated.deuteronMass = pureFunctionsAny_generated.det = pureFunctionsAny_generated.den = pureFunctionsAny_generated.deepEqual = pureFunctionsAny_generated.cumsum = pureFunctionsAny_generated.cube = pureFunctionsAny_generated.ctranspose = pureFunctionsAny_generated.csch = pureFunctionsAny_generated.csc = pureFunctionsAny_generated.cross = pureFunctionsAny_generated.createUnit = pureFunctionsAny_generated.count = pureFunctionsAny_generated.coulombConstant = pureFunctionsAny_generated.coulomb = pureFunctionsAny_generated.coth = pureFunctionsAny_generated.cot = pureFunctionsAny_generated.cosh = pureFunctionsAny_generated.cos = pureFunctionsAny_generated.corr = pureFunctionsAny_generated.conj = pureFunctionsAny_generated.conductanceQuantum = pureFunctionsAny_generated.concat = pureFunctionsAny_generated.composition = pureFunctionsAny_generated.complex = pureFunctionsAny_generated.compareText = pureFunctionsAny_generated.compareNatural = pureFunctionsAny_generated.compare = pureFunctionsAny_generated.combinationsWithRep = pureFunctionsAny_generated.combinations = pureFunctionsAny_generated.column = pureFunctionsAny_generated.clone = pureFunctionsAny_generated.classicalElectronRadius = pureFunctionsAny_generated.ceil = pureFunctionsAny_generated.cbrt = pureFunctionsAny_generated.catalan = pureFunctionsAny_generated.boolean = pureFunctionsAny_generated.boltzmann = pureFunctionsAny_generated.bohrRadius = pureFunctionsAny_generated.bohrMagneton = pureFunctionsAny_generated.bitXor = pureFunctionsAny_generated.bitOr = pureFunctionsAny_generated.bitNot = pureFunctionsAny_generated.bitAnd = pureFunctionsAny_generated.bin = pureFunctionsAny_generated.bignumber = pureFunctionsAny_generated.bigint = pureFunctionsAny_generated.bernoulli = pureFunctionsAny_generated.bellNumbers = pureFunctionsAny_generated.avogadro = pureFunctionsAny_generated.atomicMass = pureFunctionsAny_generated.atanh = pureFunctionsAny_generated.atan2 = pureFunctionsAny_generated.atan = pureFunctionsAny_generated.asinh = pureFunctionsAny_generated.asin = pureFunctionsAny_generated.asech = pureFunctionsAny_generated.asec = pureFunctionsAny_generated.arg = pureFunctionsAny_generated.apply = pureFunctionsAny_generated.and = pureFunctionsAny_generated.addScalar = pureFunctionsAny_generated.add = pureFunctionsAny_generated.acsch = pureFunctionsAny_generated.acsc = pureFunctionsAny_generated.acoth = pureFunctionsAny_generated.acot = pureFunctionsAny_generated.acosh = pureFunctionsAny_generated.acos = pureFunctionsAny_generated.abs = pureFunctionsAny_generated._true = pureFunctionsAny_generated._null = pureFunctionsAny_generated._false = pureFunctionsAny_generated._NaN = pureFunctionsAny_generated._Infinity = pureFunctionsAny_generated.Unit = pureFunctionsAny_generated.SparseMatrix = pureFunctionsAny_generated.Spa = pureFunctionsAny_generated.SQRT2 = pureFunctionsAny_generated.SQRT1_2 = pureFunctionsAny_generated.ResultSet = pureFunctionsAny_generated.Range = pureFunctionsAny_generated.Matrix = pureFunctionsAny_generated.LOG2E = pureFunctionsAny_generated.LOG10E = pureFunctionsAny_generated.LN2 = pureFunctionsAny_generated.LN10 = pureFunctionsAny_generated.Index = pureFunctionsAny_generated.ImmutableDenseMatrix = pureFunctionsAny_generated.Fraction = pureFunctionsAny_generated.FibonacciHeap = pureFunctionsAny_generated.DenseMatrix = pureFunctionsAny_generated.Complex = pureFunctionsAny_generated.BigNumber = void 0;
	pureFunctionsAny_generated.not = pureFunctionsAny_generated.norm = pureFunctionsAny_generated.neutronMass = pureFunctionsAny_generated.multiplyScalar = pureFunctionsAny_generated.multiply = pureFunctionsAny_generated.multinomial = pureFunctionsAny_generated.molarVolume = pureFunctionsAny_generated.molarPlanckConstant = pureFunctionsAny_generated.molarMassC12 = pureFunctionsAny_generated.molarMass = pureFunctionsAny_generated.mode = pureFunctionsAny_generated.mod = pureFunctionsAny_generated.min = pureFunctionsAny_generated.median = pureFunctionsAny_generated.mean = pureFunctionsAny_generated.max = pureFunctionsAny_generated.matrixFromRows = pureFunctionsAny_generated.matrixFromFunction = pureFunctionsAny_generated.matrixFromColumns = pureFunctionsAny_generated.matrix = pureFunctionsAny_generated.mapSlices = pureFunctionsAny_generated.map = pureFunctionsAny_generated.magneticFluxQuantum = pureFunctionsAny_generated.magneticConstant = pureFunctionsAny_generated.mad = pureFunctionsAny_generated.lyap = pureFunctionsAny_generated.lusolve = pureFunctionsAny_generated.lup = pureFunctionsAny_generated.lsolveAll = pureFunctionsAny_generated.lsolve = pureFunctionsAny_generated.loschmidt = pureFunctionsAny_generated.log2 = pureFunctionsAny_generated.log1p = pureFunctionsAny_generated.log10 = pureFunctionsAny_generated.log = pureFunctionsAny_generated.lgamma = pureFunctionsAny_generated.leftShift = pureFunctionsAny_generated.lcm = pureFunctionsAny_generated.largerEq = pureFunctionsAny_generated.larger = pureFunctionsAny_generated.kron = pureFunctionsAny_generated.klitzing = pureFunctionsAny_generated.kldivergence = pureFunctionsAny_generated.isZero = pureFunctionsAny_generated.isPrime = pureFunctionsAny_generated.isPositive = pureFunctionsAny_generated.isNumeric = pureFunctionsAny_generated.isNegative = pureFunctionsAny_generated.isNaN = pureFunctionsAny_generated.isInteger = pureFunctionsAny_generated.isFinite = pureFunctionsAny_generated.isBounded = pureFunctionsAny_generated.invmod = pureFunctionsAny_generated.inverseConductanceQuantum = pureFunctionsAny_generated.inv = pureFunctionsAny_generated.intersect = pureFunctionsAny_generated.index = pureFunctionsAny_generated.im = pureFunctionsAny_generated.ifft = pureFunctionsAny_generated.identity = pureFunctionsAny_generated.i = pureFunctionsAny_generated.hypot = pureFunctionsAny_generated.hex = pureFunctionsAny_generated.hasNumericValue = pureFunctionsAny_generated.hartreeEnergy = pureFunctionsAny_generated.gravity = pureFunctionsAny_generated.gravitationConstant = pureFunctionsAny_generated.getMatrixDataType = pureFunctionsAny_generated.gcd = pureFunctionsAny_generated.gasConstant = pureFunctionsAny_generated.gamma = pureFunctionsAny_generated.freqz = pureFunctionsAny_generated.fraction = pureFunctionsAny_generated.format = pureFunctionsAny_generated.forEach = pureFunctionsAny_generated.floor = pureFunctionsAny_generated.flatten = pureFunctionsAny_generated.fix = pureFunctionsAny_generated.firstRadiation = pureFunctionsAny_generated.fineStructure = pureFunctionsAny_generated.filter = pureFunctionsAny_generated.fft = pureFunctionsAny_generated.fermiCoupling = pureFunctionsAny_generated.faraday = pureFunctionsAny_generated.factorial = pureFunctionsAny_generated.expm1 = pureFunctionsAny_generated.expm = pureFunctionsAny_generated.exp = pureFunctionsAny_generated.erf = pureFunctionsAny_generated.equalText = pureFunctionsAny_generated.equalScalar = pureFunctionsAny_generated.equal = pureFunctionsAny_generated.elementaryCharge = pureFunctionsAny_generated.electronMass = pureFunctionsAny_generated.electricConstant = pureFunctionsAny_generated.eigs = pureFunctionsAny_generated.efimovFactor = pureFunctionsAny_generated.e = pureFunctionsAny_generated.dotPow = pureFunctionsAny_generated.dotMultiply = void 0;
	pureFunctionsAny_generated.usolve = pureFunctionsAny_generated.unit = pureFunctionsAny_generated.unequal = pureFunctionsAny_generated.unaryPlus = pureFunctionsAny_generated.unaryMinus = pureFunctionsAny_generated.typed = pureFunctionsAny_generated.typeOf = pureFunctionsAny_generated.transpose = pureFunctionsAny_generated.trace = pureFunctionsAny_generated.toBest = pureFunctionsAny_generated.to = pureFunctionsAny_generated.thomsonCrossSection = pureFunctionsAny_generated.tau = pureFunctionsAny_generated.tanh = pureFunctionsAny_generated.tan = pureFunctionsAny_generated.sylvester = pureFunctionsAny_generated.sum = pureFunctionsAny_generated.subtractScalar = pureFunctionsAny_generated.subtract = pureFunctionsAny_generated.subset = pureFunctionsAny_generated.string = pureFunctionsAny_generated.stirlingS2 = pureFunctionsAny_generated.stefanBoltzmann = pureFunctionsAny_generated.std = pureFunctionsAny_generated.squeeze = pureFunctionsAny_generated.square = pureFunctionsAny_generated.sqrtm = pureFunctionsAny_generated.sqrt = pureFunctionsAny_generated.splitUnit = pureFunctionsAny_generated.speedOfLight = pureFunctionsAny_generated.sparse = pureFunctionsAny_generated.sort = pureFunctionsAny_generated.solveODE = pureFunctionsAny_generated.smallerEq = pureFunctionsAny_generated.smaller = pureFunctionsAny_generated.slu = pureFunctionsAny_generated.size = pureFunctionsAny_generated.sinh = pureFunctionsAny_generated.sin = pureFunctionsAny_generated.sign = pureFunctionsAny_generated.setUnion = pureFunctionsAny_generated.setSymDifference = pureFunctionsAny_generated.setSize = pureFunctionsAny_generated.setPowerset = pureFunctionsAny_generated.setMultiplicity = pureFunctionsAny_generated.setIsSubset = pureFunctionsAny_generated.setIntersect = pureFunctionsAny_generated.setDistinct = pureFunctionsAny_generated.setDifference = pureFunctionsAny_generated.setCartesian = pureFunctionsAny_generated.secondRadiation = pureFunctionsAny_generated.sech = pureFunctionsAny_generated.sec = pureFunctionsAny_generated.schur = pureFunctionsAny_generated.sackurTetrode = pureFunctionsAny_generated.rydberg = pureFunctionsAny_generated.row = pureFunctionsAny_generated.round = pureFunctionsAny_generated.rotationMatrix = pureFunctionsAny_generated.rotate = pureFunctionsAny_generated.rightLogShift = pureFunctionsAny_generated.rightArithShift = pureFunctionsAny_generated.resize = pureFunctionsAny_generated.reshape = pureFunctionsAny_generated.replacer = pureFunctionsAny_generated.reducedPlanckConstant = pureFunctionsAny_generated.re = pureFunctionsAny_generated.range = pureFunctionsAny_generated.randomInt = pureFunctionsAny_generated.random = pureFunctionsAny_generated.quantumOfCirculation = pureFunctionsAny_generated.quantileSeq = pureFunctionsAny_generated.qr = pureFunctionsAny_generated.protonMass = pureFunctionsAny_generated.prod = pureFunctionsAny_generated.print = pureFunctionsAny_generated.pow = pureFunctionsAny_generated.polynomialRoot = pureFunctionsAny_generated.planckTime = pureFunctionsAny_generated.planckTemperature = pureFunctionsAny_generated.planckMass = pureFunctionsAny_generated.planckLength = pureFunctionsAny_generated.planckConstant = pureFunctionsAny_generated.planckCharge = pureFunctionsAny_generated.pinv = pureFunctionsAny_generated.pickRandom = pureFunctionsAny_generated.pi = pureFunctionsAny_generated.phi = pureFunctionsAny_generated.permutations = pureFunctionsAny_generated.partitionSelect = pureFunctionsAny_generated.or = pureFunctionsAny_generated.ones = pureFunctionsAny_generated.oct = pureFunctionsAny_generated.numeric = pureFunctionsAny_generated.number = pureFunctionsAny_generated.num = pureFunctionsAny_generated.nullish = pureFunctionsAny_generated.nuclearMagneton = pureFunctionsAny_generated.nthRoots = pureFunctionsAny_generated.nthRoot = void 0;
	pureFunctionsAny_generated.zpk2tf = pureFunctionsAny_generated.zeta = pureFunctionsAny_generated.zeros = pureFunctionsAny_generated.xor = pureFunctionsAny_generated.xgcd = pureFunctionsAny_generated.wienDisplacement = pureFunctionsAny_generated.weakMixingAngle = pureFunctionsAny_generated.version = pureFunctionsAny_generated.variance = pureFunctionsAny_generated.vacuumImpedance = pureFunctionsAny_generated.usolveAll = void 0;
	var _configReadonly = requireConfigReadonly();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	const BigNumber = pureFunctionsAny_generated.BigNumber = /* #__PURE__ */(0, _factoriesAny.createBigNumberClass)({
	  config: _configReadonly.config
	});
	const Complex = pureFunctionsAny_generated.Complex = /* #__PURE__ */(0, _factoriesAny.createComplexClass)({});
	pureFunctionsAny_generated.e = /* #__PURE__ */(0, _factoriesAny.createE)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated._false = /* #__PURE__ */(0, _factoriesAny.createFalse)({});
	pureFunctionsAny_generated.fineStructure = /* #__PURE__ */(0, _factoriesAny.createFineStructure)({
	  BigNumber,
	  config: _configReadonly.config
	});
	const Fraction = pureFunctionsAny_generated.Fraction = /* #__PURE__ */(0, _factoriesAny.createFractionClass)({});
	const i = pureFunctionsAny_generated.i = /* #__PURE__ */(0, _factoriesAny.createI)({
	  Complex
	});
	pureFunctionsAny_generated._Infinity = /* #__PURE__ */(0, _factoriesAny.createInfinity)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.LN10 = /* #__PURE__ */(0, _factoriesAny.createLN10)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.LOG10E = /* #__PURE__ */(0, _factoriesAny.createLOG10E)({
	  BigNumber,
	  config: _configReadonly.config
	});
	const Matrix = pureFunctionsAny_generated.Matrix = /* #__PURE__ */(0, _factoriesAny.createMatrixClass)({});
	pureFunctionsAny_generated._NaN = /* #__PURE__ */(0, _factoriesAny.createNaN)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated._null = /* #__PURE__ */(0, _factoriesAny.createNull)({});
	pureFunctionsAny_generated.phi = /* #__PURE__ */(0, _factoriesAny.createPhi)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.Range = /* #__PURE__ */(0, _factoriesAny.createRangeClass)({});
	pureFunctionsAny_generated.ResultSet = /* #__PURE__ */(0, _factoriesAny.createResultSet)({});
	pureFunctionsAny_generated.SQRT1_2 = /* #__PURE__ */(0, _factoriesAny.createSQRT1_2)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.sackurTetrode = /* #__PURE__ */(0, _factoriesAny.createSackurTetrode)({
	  BigNumber,
	  config: _configReadonly.config
	});
	const tau = pureFunctionsAny_generated.tau = /* #__PURE__ */(0, _factoriesAny.createTau)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated._true = /* #__PURE__ */(0, _factoriesAny.createTrue)({});
	pureFunctionsAny_generated.version = /* #__PURE__ */(0, _factoriesAny.createVersion)({});
	const DenseMatrix = pureFunctionsAny_generated.DenseMatrix = /* #__PURE__ */(0, _factoriesAny.createDenseMatrixClass)({
	  Matrix,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.efimovFactor = /* #__PURE__ */(0, _factoriesAny.createEfimovFactor)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.LN2 = /* #__PURE__ */(0, _factoriesAny.createLN2)({
	  BigNumber,
	  config: _configReadonly.config
	});
	const pi = pureFunctionsAny_generated.pi = /* #__PURE__ */(0, _factoriesAny.createPi)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.replacer = /* #__PURE__ */(0, _factoriesAny.createReplacer)({});
	pureFunctionsAny_generated.SQRT2 = /* #__PURE__ */(0, _factoriesAny.createSQRT2)({
	  BigNumber,
	  config: _configReadonly.config
	});
	const typed = pureFunctionsAny_generated.typed = /* #__PURE__ */(0, _factoriesAny.createTyped)({
	  BigNumber,
	  Complex,
	  DenseMatrix,
	  Fraction
	});
	pureFunctionsAny_generated.weakMixingAngle = /* #__PURE__ */(0, _factoriesAny.createWeakMixingAngle)({
	  BigNumber,
	  config: _configReadonly.config
	});
	const abs = pureFunctionsAny_generated.abs = /* #__PURE__ */(0, _factoriesAny.createAbs)({
	  typed
	});
	pureFunctionsAny_generated.acos = /* #__PURE__ */(0, _factoriesAny.createAcos)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.acot = /* #__PURE__ */(0, _factoriesAny.createAcot)({
	  BigNumber,
	  typed
	});
	pureFunctionsAny_generated.acsc = /* #__PURE__ */(0, _factoriesAny.createAcsc)({
	  BigNumber,
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	const addScalar = pureFunctionsAny_generated.addScalar = /* #__PURE__ */(0, _factoriesAny.createAddScalar)({
	  typed
	});
	pureFunctionsAny_generated.arg = /* #__PURE__ */(0, _factoriesAny.createArg)({
	  typed
	});
	pureFunctionsAny_generated.asech = /* #__PURE__ */(0, _factoriesAny.createAsech)({
	  BigNumber,
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.asinh = /* #__PURE__ */(0, _factoriesAny.createAsinh)({
	  typed
	});
	const atan = pureFunctionsAny_generated.atan = /* #__PURE__ */(0, _factoriesAny.createAtan)({
	  typed
	});
	pureFunctionsAny_generated.atanh = /* #__PURE__ */(0, _factoriesAny.createAtanh)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.bigint = /* #__PURE__ */(0, _factoriesAny.createBigint)({
	  typed
	});
	pureFunctionsAny_generated.bitNot = /* #__PURE__ */(0, _factoriesAny.createBitNot)({
	  typed
	});
	pureFunctionsAny_generated.boolean = /* #__PURE__ */(0, _factoriesAny.createBoolean)({
	  typed
	});
	pureFunctionsAny_generated.clone = /* #__PURE__ */(0, _factoriesAny.createClone)({
	  typed
	});
	const combinations = pureFunctionsAny_generated.combinations = /* #__PURE__ */(0, _factoriesAny.createCombinations)({
	  typed
	});
	const complex = pureFunctionsAny_generated.complex = /* #__PURE__ */(0, _factoriesAny.createComplex)({
	  Complex,
	  typed
	});
	const conj = pureFunctionsAny_generated.conj = /* #__PURE__ */(0, _factoriesAny.createConj)({
	  typed
	});
	const cos = pureFunctionsAny_generated.cos = /* #__PURE__ */(0, _factoriesAny.createCos)({
	  typed
	});
	pureFunctionsAny_generated.cot = /* #__PURE__ */(0, _factoriesAny.createCot)({
	  BigNumber,
	  typed
	});
	pureFunctionsAny_generated.csc = /* #__PURE__ */(0, _factoriesAny.createCsc)({
	  BigNumber,
	  typed
	});
	pureFunctionsAny_generated.cube = /* #__PURE__ */(0, _factoriesAny.createCube)({
	  typed
	});
	const equalScalar = pureFunctionsAny_generated.equalScalar = /* #__PURE__ */(0, _factoriesAny.createEqualScalar)({
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.erf = /* #__PURE__ */(0, _factoriesAny.createErf)({
	  typed
	});
	const exp = pureFunctionsAny_generated.exp = /* #__PURE__ */(0, _factoriesAny.createExp)({
	  typed
	});
	pureFunctionsAny_generated.expm1 = /* #__PURE__ */(0, _factoriesAny.createExpm1)({
	  Complex,
	  typed
	});
	pureFunctionsAny_generated.filter = /* #__PURE__ */(0, _factoriesAny.createFilter)({
	  typed
	});
	const flatten = pureFunctionsAny_generated.flatten = /* #__PURE__ */(0, _factoriesAny.createFlatten)({
	  typed
	});
	pureFunctionsAny_generated.forEach = /* #__PURE__ */(0, _factoriesAny.createForEach)({
	  typed
	});
	const format = pureFunctionsAny_generated.format = /* #__PURE__ */(0, _factoriesAny.createFormat)({
	  typed
	});
	const getMatrixDataType = pureFunctionsAny_generated.getMatrixDataType = /* #__PURE__ */(0, _factoriesAny.createGetMatrixDataType)({
	  typed
	});
	pureFunctionsAny_generated.hex = /* #__PURE__ */(0, _factoriesAny.createHex)({
	  format,
	  typed
	});
	const im = pureFunctionsAny_generated.im = /* #__PURE__ */(0, _factoriesAny.createIm)({
	  typed
	});
	const isBounded = pureFunctionsAny_generated.isBounded = /* #__PURE__ */(0, _factoriesAny.createIsBounded)({
	  typed
	});
	const isNaN = pureFunctionsAny_generated.isNaN = /* #__PURE__ */(0, _factoriesAny.createIsNaN)({
	  typed
	});
	const isNumeric = pureFunctionsAny_generated.isNumeric = /* #__PURE__ */(0, _factoriesAny.createIsNumeric)({
	  typed
	});
	pureFunctionsAny_generated.isPrime = /* #__PURE__ */(0, _factoriesAny.createIsPrime)({
	  typed
	});
	pureFunctionsAny_generated.LOG2E = /* #__PURE__ */(0, _factoriesAny.createLOG2E)({
	  BigNumber,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.lgamma = /* #__PURE__ */(0, _factoriesAny.createLgamma)({
	  Complex,
	  typed
	});
	pureFunctionsAny_generated.log10 = /* #__PURE__ */(0, _factoriesAny.createLog10)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	const log2 = pureFunctionsAny_generated.log2 = /* #__PURE__ */(0, _factoriesAny.createLog2)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	const map = pureFunctionsAny_generated.map = /* #__PURE__ */(0, _factoriesAny.createMap)({
	  typed
	});
	pureFunctionsAny_generated.mode = /* #__PURE__ */(0, _factoriesAny.createMode)({
	  isNaN,
	  isNumeric,
	  typed
	});
	const multiplyScalar = pureFunctionsAny_generated.multiplyScalar = /* #__PURE__ */(0, _factoriesAny.createMultiplyScalar)({
	  typed
	});
	const not = pureFunctionsAny_generated.not = /* #__PURE__ */(0, _factoriesAny.createNot)({
	  typed
	});
	const number = pureFunctionsAny_generated.number = /* #__PURE__ */(0, _factoriesAny.createNumber)({
	  typed
	});
	pureFunctionsAny_generated.oct = /* #__PURE__ */(0, _factoriesAny.createOct)({
	  format,
	  typed
	});
	pureFunctionsAny_generated.pickRandom = /* #__PURE__ */(0, _factoriesAny.createPickRandom)({
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.print = /* #__PURE__ */(0, _factoriesAny.createPrint)({
	  typed
	});
	pureFunctionsAny_generated.random = /* #__PURE__ */(0, _factoriesAny.createRandom)({
	  config: _configReadonly.config,
	  typed
	});
	const re = pureFunctionsAny_generated.re = /* #__PURE__ */(0, _factoriesAny.createRe)({
	  typed
	});
	pureFunctionsAny_generated.sec = /* #__PURE__ */(0, _factoriesAny.createSec)({
	  BigNumber,
	  typed
	});
	const sign = pureFunctionsAny_generated.sign = /* #__PURE__ */(0, _factoriesAny.createSign)({
	  BigNumber,
	  Fraction,
	  complex,
	  typed
	});
	const sin = pureFunctionsAny_generated.sin = /* #__PURE__ */(0, _factoriesAny.createSin)({
	  typed
	});
	const size = pureFunctionsAny_generated.size = /* #__PURE__ */(0, _factoriesAny.createSize)({
	  typed
	});
	const SparseMatrix = pureFunctionsAny_generated.SparseMatrix = /* #__PURE__ */(0, _factoriesAny.createSparseMatrixClass)({
	  Matrix,
	  equalScalar,
	  typed
	});
	pureFunctionsAny_generated.splitUnit = /* #__PURE__ */(0, _factoriesAny.createSplitUnit)({
	  typed
	});
	pureFunctionsAny_generated.square = /* #__PURE__ */(0, _factoriesAny.createSquare)({
	  typed
	});
	pureFunctionsAny_generated.string = /* #__PURE__ */(0, _factoriesAny.createString)({
	  typed
	});
	const subtractScalar = pureFunctionsAny_generated.subtractScalar = /* #__PURE__ */(0, _factoriesAny.createSubtractScalar)({
	  typed
	});
	pureFunctionsAny_generated.tan = /* #__PURE__ */(0, _factoriesAny.createTan)({
	  typed
	});
	pureFunctionsAny_generated.toBest = /* #__PURE__ */(0, _factoriesAny.createToBest)({
	  typed
	});
	const typeOf = pureFunctionsAny_generated.typeOf = /* #__PURE__ */(0, _factoriesAny.createTypeOf)({
	  typed
	});
	pureFunctionsAny_generated.acosh = /* #__PURE__ */(0, _factoriesAny.createAcosh)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.acsch = /* #__PURE__ */(0, _factoriesAny.createAcsch)({
	  BigNumber,
	  typed
	});
	pureFunctionsAny_generated.asec = /* #__PURE__ */(0, _factoriesAny.createAsec)({
	  BigNumber,
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	const bignumber = pureFunctionsAny_generated.bignumber = /* #__PURE__ */(0, _factoriesAny.createBignumber)({
	  BigNumber,
	  typed
	});
	pureFunctionsAny_generated.combinationsWithRep = /* #__PURE__ */(0, _factoriesAny.createCombinationsWithRep)({
	  typed
	});
	pureFunctionsAny_generated.cosh = /* #__PURE__ */(0, _factoriesAny.createCosh)({
	  typed
	});
	pureFunctionsAny_generated.csch = /* #__PURE__ */(0, _factoriesAny.createCsch)({
	  BigNumber,
	  typed
	});
	const dot = pureFunctionsAny_generated.dot = /* #__PURE__ */(0, _factoriesAny.createDot)({
	  addScalar,
	  conj,
	  multiplyScalar,
	  size,
	  typed
	});
	pureFunctionsAny_generated.hasNumericValue = /* #__PURE__ */(0, _factoriesAny.createHasNumericValue)({
	  isNumeric,
	  typed
	});
	pureFunctionsAny_generated.isFinite = /* #__PURE__ */(0, _factoriesAny.createIsFinite)({
	  isBounded,
	  map,
	  typed
	});
	const isNegative = pureFunctionsAny_generated.isNegative = /* #__PURE__ */(0, _factoriesAny.createIsNegative)({
	  config: _configReadonly.config,
	  typed
	});
	const isZero = pureFunctionsAny_generated.isZero = /* #__PURE__ */(0, _factoriesAny.createIsZero)({
	  equalScalar,
	  typed
	});
	const matrix = pureFunctionsAny_generated.matrix = /* #__PURE__ */(0, _factoriesAny.createMatrix)({
	  DenseMatrix,
	  Matrix,
	  SparseMatrix,
	  typed
	});
	pureFunctionsAny_generated.matrixFromFunction = /* #__PURE__ */(0, _factoriesAny.createMatrixFromFunction)({
	  isZero,
	  matrix,
	  typed
	});
	const multiply = pureFunctionsAny_generated.multiply = /* #__PURE__ */(0, _factoriesAny.createMultiply)({
	  addScalar,
	  dot,
	  equalScalar,
	  matrix,
	  multiplyScalar,
	  typed
	});
	pureFunctionsAny_generated.ones = /* #__PURE__ */(0, _factoriesAny.createOnes)({
	  BigNumber,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.randomInt = /* #__PURE__ */(0, _factoriesAny.createRandomInt)({
	  config: _configReadonly.config,
	  log2,
	  typed
	});
	pureFunctionsAny_generated.resize = /* #__PURE__ */(0, _factoriesAny.createResize)({
	  config: _configReadonly.config,
	  matrix
	});
	pureFunctionsAny_generated.sech = /* #__PURE__ */(0, _factoriesAny.createSech)({
	  BigNumber,
	  typed
	});
	pureFunctionsAny_generated.sinh = /* #__PURE__ */(0, _factoriesAny.createSinh)({
	  typed
	});
	pureFunctionsAny_generated.sparse = /* #__PURE__ */(0, _factoriesAny.createSparse)({
	  SparseMatrix,
	  typed
	});
	const sqrt = pureFunctionsAny_generated.sqrt = /* #__PURE__ */(0, _factoriesAny.createSqrt)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.squeeze = /* #__PURE__ */(0, _factoriesAny.createSqueeze)({
	  typed
	});
	pureFunctionsAny_generated.tanh = /* #__PURE__ */(0, _factoriesAny.createTanh)({
	  typed
	});
	const transpose = pureFunctionsAny_generated.transpose = /* #__PURE__ */(0, _factoriesAny.createTranspose)({
	  matrix,
	  typed
	});
	const xgcd = pureFunctionsAny_generated.xgcd = /* #__PURE__ */(0, _factoriesAny.createXgcd)({
	  BigNumber,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	const zeros = pureFunctionsAny_generated.zeros = /* #__PURE__ */(0, _factoriesAny.createZeros)({
	  BigNumber,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.acoth = /* #__PURE__ */(0, _factoriesAny.createAcoth)({
	  BigNumber,
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.asin = /* #__PURE__ */(0, _factoriesAny.createAsin)({
	  Complex,
	  config: _configReadonly.config,
	  typed
	});
	pureFunctionsAny_generated.bin = /* #__PURE__ */(0, _factoriesAny.createBin)({
	  format,
	  typed
	});
	pureFunctionsAny_generated.coth = /* #__PURE__ */(0, _factoriesAny.createCoth)({
	  BigNumber,
	  typed
	});
	const ctranspose = pureFunctionsAny_generated.ctranspose = /* #__PURE__ */(0, _factoriesAny.createCtranspose)({
	  conj,
	  transpose,
	  typed
	});
	const diag = pureFunctionsAny_generated.diag = /* #__PURE__ */(0, _factoriesAny.createDiag)({
	  DenseMatrix,
	  SparseMatrix,
	  matrix,
	  typed
	});
	const equal = pureFunctionsAny_generated.equal = /* #__PURE__ */(0, _factoriesAny.createEqual)({
	  DenseMatrix,
	  SparseMatrix,
	  equalScalar,
	  matrix,
	  typed
	});
	const fraction = pureFunctionsAny_generated.fraction = /* #__PURE__ */(0, _factoriesAny.createFraction)({
	  Fraction,
	  typed
	});
	const identity = pureFunctionsAny_generated.identity = /* #__PURE__ */(0, _factoriesAny.createIdentity)({
	  BigNumber,
	  DenseMatrix,
	  SparseMatrix,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	const isInteger = pureFunctionsAny_generated.isInteger = /* #__PURE__ */(0, _factoriesAny.createIsInteger)({
	  equal,
	  typed
	});
	pureFunctionsAny_generated.kron = /* #__PURE__ */(0, _factoriesAny.createKron)({
	  matrix,
	  multiplyScalar,
	  typed
	});
	const mapSlices = pureFunctionsAny_generated.mapSlices = /* #__PURE__ */(0, _factoriesAny.createMapSlices)({
	  isInteger,
	  typed
	});
	pureFunctionsAny_generated.apply = mapSlices;
	const matrixFromColumns = pureFunctionsAny_generated.matrixFromColumns = /* #__PURE__ */(0, _factoriesAny.createMatrixFromColumns)({
	  flatten,
	  matrix,
	  size,
	  typed
	});
	pureFunctionsAny_generated.num = /* #__PURE__ */(0, _factoriesAny.createNum)({
	  fraction,
	  typed
	});
	const reshape = pureFunctionsAny_generated.reshape = /* #__PURE__ */(0, _factoriesAny.createReshape)({
	  isInteger,
	  matrix,
	  typed
	});
	const round = pureFunctionsAny_generated.round = /* #__PURE__ */(0, _factoriesAny.createRound)({
	  BigNumber,
	  DenseMatrix,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  typed,
	  zeros
	});
	const unaryMinus = pureFunctionsAny_generated.unaryMinus = /* #__PURE__ */(0, _factoriesAny.createUnaryMinus)({
	  typed
	});
	pureFunctionsAny_generated.bernoulli = /* #__PURE__ */(0, _factoriesAny.createBernoulli)({
	  BigNumber,
	  Fraction,
	  config: _configReadonly.config,
	  isInteger,
	  number,
	  typed
	});
	const cbrt = pureFunctionsAny_generated.cbrt = /* #__PURE__ */(0, _factoriesAny.createCbrt)({
	  BigNumber,
	  Complex,
	  Fraction,
	  config: _configReadonly.config,
	  isNegative,
	  matrix,
	  typed,
	  unaryMinus
	});
	const concat = pureFunctionsAny_generated.concat = /* #__PURE__ */(0, _factoriesAny.createConcat)({
	  isInteger,
	  matrix,
	  typed
	});
	const deepEqual = pureFunctionsAny_generated.deepEqual = /* #__PURE__ */(0, _factoriesAny.createDeepEqual)({
	  equal,
	  typed
	});
	pureFunctionsAny_generated.dotMultiply = /* #__PURE__ */(0, _factoriesAny.createDotMultiply)({
	  concat,
	  equalScalar,
	  matrix,
	  multiplyScalar,
	  typed
	});
	const floor = pureFunctionsAny_generated.floor = /* #__PURE__ */(0, _factoriesAny.createFloor)({
	  DenseMatrix,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  round,
	  typed,
	  zeros
	});
	pureFunctionsAny_generated.gcd = /* #__PURE__ */(0, _factoriesAny.createGcd)({
	  BigNumber,
	  DenseMatrix,
	  concat,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  round,
	  typed,
	  zeros
	});
	const isPositive = pureFunctionsAny_generated.isPositive = /* #__PURE__ */(0, _factoriesAny.createIsPositive)({
	  config: _configReadonly.config,
	  typed
	});
	const larger = pureFunctionsAny_generated.larger = /* #__PURE__ */(0, _factoriesAny.createLarger)({
	  DenseMatrix,
	  SparseMatrix,
	  bignumber,
	  concat,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.lcm = /* #__PURE__ */(0, _factoriesAny.createLcm)({
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.leftShift = /* #__PURE__ */(0, _factoriesAny.createLeftShift)({
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  typed,
	  zeros
	});
	const mod = pureFunctionsAny_generated.mod = /* #__PURE__ */(0, _factoriesAny.createMod)({
	  DenseMatrix,
	  concat,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  round,
	  typed,
	  zeros
	});
	pureFunctionsAny_generated.nthRoot = /* #__PURE__ */(0, _factoriesAny.createNthRoot)({
	  BigNumber,
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.nullish = /* #__PURE__ */(0, _factoriesAny.createNullish)({
	  deepEqual,
	  flatten,
	  matrix,
	  size,
	  typed
	});
	const numeric = pureFunctionsAny_generated.numeric = /* #__PURE__ */(0, _factoriesAny.createNumeric)({
	  bignumber,
	  fraction,
	  number
	});
	const prod = pureFunctionsAny_generated.prod = /* #__PURE__ */(0, _factoriesAny.createProd)({
	  config: _configReadonly.config,
	  multiplyScalar,
	  numeric,
	  typed
	});
	pureFunctionsAny_generated.rightArithShift = /* #__PURE__ */(0, _factoriesAny.createRightArithShift)({
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  typed,
	  zeros
	});
	const smaller = pureFunctionsAny_generated.smaller = /* #__PURE__ */(0, _factoriesAny.createSmaller)({
	  DenseMatrix,
	  SparseMatrix,
	  bignumber,
	  concat,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	const subtract = pureFunctionsAny_generated.subtract = /* #__PURE__ */(0, _factoriesAny.createSubtract)({
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  subtractScalar,
	  typed,
	  unaryMinus
	});
	pureFunctionsAny_generated.to = /* #__PURE__ */(0, _factoriesAny.createTo)({
	  concat,
	  matrix,
	  typed
	});
	const unaryPlus = pureFunctionsAny_generated.unaryPlus = /* #__PURE__ */(0, _factoriesAny.createUnaryPlus)({
	  config: _configReadonly.config,
	  numeric,
	  typed
	});
	pureFunctionsAny_generated.xor = /* #__PURE__ */(0, _factoriesAny.createXor)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  matrix,
	  typed
	});
	const add = pureFunctionsAny_generated.add = /* #__PURE__ */(0, _factoriesAny.createAdd)({
	  DenseMatrix,
	  SparseMatrix,
	  addScalar,
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.atan2 = /* #__PURE__ */(0, _factoriesAny.createAtan2)({
	  BigNumber,
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.bitAnd = /* #__PURE__ */(0, _factoriesAny.createBitAnd)({
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.bitOr = /* #__PURE__ */(0, _factoriesAny.createBitOr)({
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.bitXor = /* #__PURE__ */(0, _factoriesAny.createBitXor)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  matrix,
	  typed
	});
	const ceil = pureFunctionsAny_generated.ceil = /* #__PURE__ */(0, _factoriesAny.createCeil)({
	  DenseMatrix,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  round,
	  typed,
	  zeros
	});
	const compare = pureFunctionsAny_generated.compare = /* #__PURE__ */(0, _factoriesAny.createCompare)({
	  BigNumber,
	  DenseMatrix,
	  Fraction,
	  concat,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  typed
	});
	const compareText = pureFunctionsAny_generated.compareText = /* #__PURE__ */(0, _factoriesAny.createCompareText)({
	  concat,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.composition = /* #__PURE__ */(0, _factoriesAny.createComposition)({
	  addScalar,
	  combinations,
	  isInteger,
	  isNegative,
	  isPositive,
	  larger,
	  typed
	});
	pureFunctionsAny_generated.count = /* #__PURE__ */(0, _factoriesAny.createCount)({
	  prod,
	  size,
	  typed
	});
	pureFunctionsAny_generated.cross = /* #__PURE__ */(0, _factoriesAny.createCross)({
	  matrix,
	  multiply,
	  subtract,
	  typed
	});
	pureFunctionsAny_generated.diff = /* #__PURE__ */(0, _factoriesAny.createDiff)({
	  matrix,
	  number,
	  subtract,
	  typed
	});
	const divideScalar = pureFunctionsAny_generated.divideScalar = /* #__PURE__ */(0, _factoriesAny.createDivideScalar)({
	  numeric,
	  typed
	});
	pureFunctionsAny_generated.equalText = /* #__PURE__ */(0, _factoriesAny.createEqualText)({
	  compareText,
	  isZero,
	  typed
	});
	const FibonacciHeap = pureFunctionsAny_generated.FibonacciHeap = /* #__PURE__ */(0, _factoriesAny.createFibonacciHeapClass)({
	  larger,
	  smaller
	});
	const fix = pureFunctionsAny_generated.fix = /* #__PURE__ */(0, _factoriesAny.createFix)({
	  Complex,
	  DenseMatrix,
	  ceil,
	  equalScalar,
	  floor,
	  matrix,
	  typed,
	  zeros
	});
	pureFunctionsAny_generated.hypot = /* #__PURE__ */(0, _factoriesAny.createHypot)({
	  abs,
	  addScalar,
	  divideScalar,
	  isPositive,
	  multiplyScalar,
	  smaller,
	  sqrt,
	  typed
	});
	const ImmutableDenseMatrix = pureFunctionsAny_generated.ImmutableDenseMatrix = /* #__PURE__ */(0, _factoriesAny.createImmutableDenseMatrixClass)({
	  DenseMatrix,
	  smaller
	});
	const Index = pureFunctionsAny_generated.Index = /* #__PURE__ */(0, _factoriesAny.createIndexClass)({
	  ImmutableDenseMatrix,
	  getMatrixDataType
	});
	pureFunctionsAny_generated.intersect = /* #__PURE__ */(0, _factoriesAny.createIntersect)({
	  abs,
	  add,
	  addScalar,
	  config: _configReadonly.config,
	  divideScalar,
	  equalScalar,
	  flatten,
	  isNumeric,
	  isZero,
	  matrix,
	  multiply,
	  multiplyScalar,
	  smaller,
	  subtract,
	  typed
	});
	pureFunctionsAny_generated.invmod = /* #__PURE__ */(0, _factoriesAny.createInvmod)({
	  BigNumber,
	  add,
	  config: _configReadonly.config,
	  equal,
	  isInteger,
	  mod,
	  smaller,
	  typed,
	  xgcd
	});
	const largerEq = pureFunctionsAny_generated.largerEq = /* #__PURE__ */(0, _factoriesAny.createLargerEq)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	const log = pureFunctionsAny_generated.log = /* #__PURE__ */(0, _factoriesAny.createLog)({
	  Complex,
	  config: _configReadonly.config,
	  divideScalar,
	  typeOf,
	  typed
	});
	const lsolve = pureFunctionsAny_generated.lsolve = /* #__PURE__ */(0, _factoriesAny.createLsolve)({
	  DenseMatrix,
	  divideScalar,
	  equalScalar,
	  matrix,
	  multiplyScalar,
	  subtractScalar,
	  typed
	});
	pureFunctionsAny_generated.matrixFromRows = /* #__PURE__ */(0, _factoriesAny.createMatrixFromRows)({
	  flatten,
	  matrix,
	  size,
	  typed
	});
	pureFunctionsAny_generated.min = /* #__PURE__ */(0, _factoriesAny.createMin)({
	  config: _configReadonly.config,
	  isNaN,
	  numeric,
	  smaller,
	  typed
	});
	pureFunctionsAny_generated.nthRoots = /* #__PURE__ */(0, _factoriesAny.createNthRoots)({
	  Complex,
	  config: _configReadonly.config,
	  divideScalar,
	  typed
	});
	const partitionSelect = pureFunctionsAny_generated.partitionSelect = /* #__PURE__ */(0, _factoriesAny.createPartitionSelect)({
	  compare,
	  isNaN,
	  isNumeric,
	  typed
	});
	const qr = pureFunctionsAny_generated.qr = /* #__PURE__ */(0, _factoriesAny.createQr)({
	  addScalar,
	  complex,
	  conj,
	  divideScalar,
	  equal,
	  identity,
	  isZero,
	  matrix,
	  multiplyScalar,
	  sign,
	  sqrt,
	  subtractScalar,
	  typed,
	  unaryMinus,
	  zeros
	});
	pureFunctionsAny_generated.rightLogShift = /* #__PURE__ */(0, _factoriesAny.createRightLogShift)({
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  typed,
	  zeros
	});
	const slu = pureFunctionsAny_generated.slu = /* #__PURE__ */(0, _factoriesAny.createSlu)({
	  SparseMatrix,
	  abs,
	  add,
	  divideScalar,
	  larger,
	  largerEq,
	  multiply,
	  subtract,
	  transpose,
	  typed
	});
	const Spa = pureFunctionsAny_generated.Spa = /* #__PURE__ */(0, _factoriesAny.createSpaClass)({
	  FibonacciHeap,
	  addScalar,
	  equalScalar
	});
	const subset = pureFunctionsAny_generated.subset = /* #__PURE__ */(0, _factoriesAny.createSubset)({
	  add,
	  matrix,
	  typed,
	  zeros
	});
	const sum = pureFunctionsAny_generated.sum = /* #__PURE__ */(0, _factoriesAny.createSum)({
	  add,
	  config: _configReadonly.config,
	  numeric,
	  typed
	});
	pureFunctionsAny_generated.trace = /* #__PURE__ */(0, _factoriesAny.createTrace)({
	  add,
	  matrix,
	  typed
	});
	const usolve = pureFunctionsAny_generated.usolve = /* #__PURE__ */(0, _factoriesAny.createUsolve)({
	  DenseMatrix,
	  divideScalar,
	  equalScalar,
	  matrix,
	  multiplyScalar,
	  subtractScalar,
	  typed
	});
	pureFunctionsAny_generated.zpk2tf = /* #__PURE__ */(0, _factoriesAny.createZpk2tf)({
	  Complex,
	  add,
	  multiply,
	  number,
	  typed
	});
	pureFunctionsAny_generated.catalan = /* #__PURE__ */(0, _factoriesAny.createCatalan)({
	  addScalar,
	  combinations,
	  divideScalar,
	  isInteger,
	  isNegative,
	  multiplyScalar,
	  typed
	});
	const compareNatural = pureFunctionsAny_generated.compareNatural = /* #__PURE__ */(0, _factoriesAny.createCompareNatural)({
	  compare,
	  typed
	});
	pureFunctionsAny_generated.cumsum = /* #__PURE__ */(0, _factoriesAny.createCumSum)({
	  add,
	  typed,
	  unaryPlus
	});
	const det = pureFunctionsAny_generated.det = /* #__PURE__ */(0, _factoriesAny.createDet)({
	  divideScalar,
	  isZero,
	  matrix,
	  multiply,
	  subtractScalar,
	  typed,
	  unaryMinus
	});
	const dotDivide = pureFunctionsAny_generated.dotDivide = /* #__PURE__ */(0, _factoriesAny.createDotDivide)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  divideScalar,
	  equalScalar,
	  matrix,
	  typed
	});
	const index = pureFunctionsAny_generated.index = /* #__PURE__ */(0, _factoriesAny.createIndex)({
	  Index,
	  typed
	});
	const inv = pureFunctionsAny_generated.inv = /* #__PURE__ */(0, _factoriesAny.createInv)({
	  abs,
	  addScalar,
	  det,
	  divideScalar,
	  identity,
	  matrix,
	  multiply,
	  typed,
	  unaryMinus
	});
	pureFunctionsAny_generated.log1p = /* #__PURE__ */(0, _factoriesAny.createLog1p)({
	  Complex,
	  config: _configReadonly.config,
	  divideScalar,
	  log,
	  typed
	});
	pureFunctionsAny_generated.lsolveAll = /* #__PURE__ */(0, _factoriesAny.createLsolveAll)({
	  DenseMatrix,
	  divideScalar,
	  equalScalar,
	  matrix,
	  multiplyScalar,
	  subtractScalar,
	  typed
	});
	const max = pureFunctionsAny_generated.max = /* #__PURE__ */(0, _factoriesAny.createMax)({
	  config: _configReadonly.config,
	  isNaN,
	  larger,
	  numeric,
	  typed
	});
	pureFunctionsAny_generated.or = /* #__PURE__ */(0, _factoriesAny.createOr)({
	  DenseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.pinv = /* #__PURE__ */(0, _factoriesAny.createPinv)({
	  Complex,
	  add,
	  ctranspose,
	  deepEqual,
	  divideScalar,
	  dot,
	  dotDivide,
	  equal,
	  inv,
	  matrix,
	  multiply,
	  typed
	});
	const pow = pureFunctionsAny_generated.pow = /* #__PURE__ */(0, _factoriesAny.createPow)({
	  Complex,
	  config: _configReadonly.config,
	  fraction,
	  identity,
	  inv,
	  matrix,
	  multiply,
	  number,
	  typed
	});
	pureFunctionsAny_generated.setCartesian = /* #__PURE__ */(0, _factoriesAny.createSetCartesian)({
	  DenseMatrix,
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	pureFunctionsAny_generated.setDistinct = /* #__PURE__ */(0, _factoriesAny.createSetDistinct)({
	  DenseMatrix,
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	pureFunctionsAny_generated.setIsSubset = /* #__PURE__ */(0, _factoriesAny.createSetIsSubset)({
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	pureFunctionsAny_generated.setPowerset = /* #__PURE__ */(0, _factoriesAny.createSetPowerset)({
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	const smallerEq = pureFunctionsAny_generated.smallerEq = /* #__PURE__ */(0, _factoriesAny.createSmallerEq)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  config: _configReadonly.config,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.sort = /* #__PURE__ */(0, _factoriesAny.createSort)({
	  compare,
	  compareNatural,
	  matrix,
	  typed
	});
	pureFunctionsAny_generated.sqrtm = /* #__PURE__ */(0, _factoriesAny.createSqrtm)({
	  abs,
	  add,
	  identity,
	  inv,
	  map,
	  max,
	  multiply,
	  size,
	  sqrt,
	  subtract,
	  typed
	});
	pureFunctionsAny_generated.unequal = /* #__PURE__ */(0, _factoriesAny.createUnequal)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  config: _configReadonly.config,
	  equalScalar,
	  matrix,
	  typed
	});
	const usolveAll = pureFunctionsAny_generated.usolveAll = /* #__PURE__ */(0, _factoriesAny.createUsolveAll)({
	  DenseMatrix,
	  divideScalar,
	  equalScalar,
	  matrix,
	  multiplyScalar,
	  subtractScalar,
	  typed
	});
	pureFunctionsAny_generated.and = /* #__PURE__ */(0, _factoriesAny.createAnd)({
	  concat,
	  equalScalar,
	  matrix,
	  not,
	  typed,
	  zeros
	});
	pureFunctionsAny_generated.den = /* #__PURE__ */(0, _factoriesAny.createDen)({
	  fraction,
	  typed
	});
	pureFunctionsAny_generated.distance = /* #__PURE__ */(0, _factoriesAny.createDistance)({
	  abs,
	  addScalar,
	  deepEqual,
	  divideScalar,
	  multiplyScalar,
	  sqrt,
	  subtractScalar,
	  typed
	});
	pureFunctionsAny_generated.dotPow = /* #__PURE__ */(0, _factoriesAny.createDotPow)({
	  DenseMatrix,
	  SparseMatrix,
	  concat,
	  equalScalar,
	  matrix,
	  pow,
	  typed
	});
	pureFunctionsAny_generated.expm = /* #__PURE__ */(0, _factoriesAny.createExpm)({
	  abs,
	  add,
	  identity,
	  inv,
	  multiply,
	  typed
	});
	const fft = pureFunctionsAny_generated.fft = /* #__PURE__ */(0, _factoriesAny.createFft)({
	  addScalar,
	  ceil,
	  conj,
	  divideScalar,
	  dotDivide,
	  exp,
	  i,
	  log2,
	  matrix,
	  multiplyScalar,
	  pow,
	  tau,
	  typed
	});
	const gamma = pureFunctionsAny_generated.gamma = /* #__PURE__ */(0, _factoriesAny.createGamma)({
	  BigNumber,
	  Complex,
	  config: _configReadonly.config,
	  multiplyScalar,
	  pow,
	  typed
	});
	pureFunctionsAny_generated.ifft = /* #__PURE__ */(0, _factoriesAny.createIfft)({
	  conj,
	  dotDivide,
	  fft,
	  typed
	});
	const lup = pureFunctionsAny_generated.lup = /* #__PURE__ */(0, _factoriesAny.createLup)({
	  DenseMatrix,
	  Spa,
	  SparseMatrix,
	  abs,
	  addScalar,
	  divideScalar,
	  equalScalar,
	  larger,
	  matrix,
	  multiplyScalar,
	  subtractScalar,
	  typed,
	  unaryMinus
	});
	const range = pureFunctionsAny_generated.range = /* #__PURE__ */(0, _factoriesAny.createRange)({
	  bignumber,
	  matrix,
	  add,
	  config: _configReadonly.config,
	  equal,
	  isPositive,
	  isZero,
	  larger,
	  largerEq,
	  smaller,
	  smallerEq,
	  typed
	});
	pureFunctionsAny_generated.row = /* #__PURE__ */(0, _factoriesAny.createRow)({
	  Index,
	  matrix,
	  range,
	  typed
	});
	const setDifference = pureFunctionsAny_generated.setDifference = /* #__PURE__ */(0, _factoriesAny.createSetDifference)({
	  DenseMatrix,
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	pureFunctionsAny_generated.setMultiplicity = /* #__PURE__ */(0, _factoriesAny.createSetMultiplicity)({
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	const setSymDifference = pureFunctionsAny_generated.setSymDifference = /* #__PURE__ */(0, _factoriesAny.createSetSymDifference)({
	  Index,
	  concat,
	  setDifference,
	  size,
	  subset,
	  typed
	});
	const Unit = pureFunctionsAny_generated.Unit = /* #__PURE__ */(0, _factoriesAny.createUnitClass)({
	  BigNumber,
	  Complex,
	  Fraction,
	  abs,
	  addScalar,
	  config: _configReadonly.config,
	  divideScalar,
	  equal,
	  fix,
	  format,
	  isNumeric,
	  multiplyScalar,
	  number,
	  pow,
	  round,
	  subtractScalar
	});
	pureFunctionsAny_generated.vacuumImpedance = /* #__PURE__ */(0, _factoriesAny.createVacuumImpedance)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.wienDisplacement = /* #__PURE__ */(0, _factoriesAny.createWienDisplacement)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.atomicMass = /* #__PURE__ */(0, _factoriesAny.createAtomicMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.bohrMagneton = /* #__PURE__ */(0, _factoriesAny.createBohrMagneton)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.boltzmann = /* #__PURE__ */(0, _factoriesAny.createBoltzmann)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	const column = pureFunctionsAny_generated.column = /* #__PURE__ */(0, _factoriesAny.createColumn)({
	  Index,
	  matrix,
	  range,
	  typed
	});
	pureFunctionsAny_generated.conductanceQuantum = /* #__PURE__ */(0, _factoriesAny.createConductanceQuantum)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.coulomb = /* #__PURE__ */(0, _factoriesAny.createCoulomb)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.createUnit = /* #__PURE__ */(0, _factoriesAny.createCreateUnit)({
	  Unit,
	  typed
	});
	pureFunctionsAny_generated.deuteronMass = /* #__PURE__ */(0, _factoriesAny.createDeuteronMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	const eigs = pureFunctionsAny_generated.eigs = /* #__PURE__ */(0, _factoriesAny.createEigs)({
	  abs,
	  add,
	  addScalar,
	  atan,
	  bignumber,
	  column,
	  complex,
	  config: _configReadonly.config,
	  cos,
	  diag,
	  divideScalar,
	  dot,
	  equal,
	  flatten,
	  im,
	  inv,
	  larger,
	  matrix,
	  matrixFromColumns,
	  multiply,
	  multiplyScalar,
	  number,
	  qr,
	  re,
	  reshape,
	  sin,
	  size,
	  smaller,
	  sqrt,
	  subtract,
	  typed,
	  usolve,
	  usolveAll
	});
	pureFunctionsAny_generated.electronMass = /* #__PURE__ */(0, _factoriesAny.createElectronMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	const factorial = pureFunctionsAny_generated.factorial = /* #__PURE__ */(0, _factoriesAny.createFactorial)({
	  gamma,
	  typed
	});
	pureFunctionsAny_generated.fermiCoupling = /* #__PURE__ */(0, _factoriesAny.createFermiCoupling)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.gasConstant = /* #__PURE__ */(0, _factoriesAny.createGasConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.gravity = /* #__PURE__ */(0, _factoriesAny.createGravity)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.klitzing = /* #__PURE__ */(0, _factoriesAny.createKlitzing)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.loschmidt = /* #__PURE__ */(0, _factoriesAny.createLoschmidt)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.magneticConstant = /* #__PURE__ */(0, _factoriesAny.createMagneticConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.molarMass = /* #__PURE__ */(0, _factoriesAny.createMolarMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.molarPlanckConstant = /* #__PURE__ */(0, _factoriesAny.createMolarPlanckConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.neutronMass = /* #__PURE__ */(0, _factoriesAny.createNeutronMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.nuclearMagneton = /* #__PURE__ */(0, _factoriesAny.createNuclearMagneton)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.permutations = /* #__PURE__ */(0, _factoriesAny.createPermutations)({
	  factorial,
	  typed
	});
	pureFunctionsAny_generated.planckConstant = /* #__PURE__ */(0, _factoriesAny.createPlanckConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.planckMass = /* #__PURE__ */(0, _factoriesAny.createPlanckMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.planckTime = /* #__PURE__ */(0, _factoriesAny.createPlanckTime)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.protonMass = /* #__PURE__ */(0, _factoriesAny.createProtonMass)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.quantumOfCirculation = /* #__PURE__ */(0, _factoriesAny.createQuantumOfCirculation)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.reducedPlanckConstant = /* #__PURE__ */(0, _factoriesAny.createReducedPlanckConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.secondRadiation = /* #__PURE__ */(0, _factoriesAny.createSecondRadiation)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.setSize = /* #__PURE__ */(0, _factoriesAny.createSetSize)({
	  compareNatural,
	  typed
	});
	pureFunctionsAny_generated.speedOfLight = /* #__PURE__ */(0, _factoriesAny.createSpeedOfLight)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.stefanBoltzmann = /* #__PURE__ */(0, _factoriesAny.createStefanBoltzmann)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.thomsonCrossSection = /* #__PURE__ */(0, _factoriesAny.createThomsonCrossSection)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.avogadro = /* #__PURE__ */(0, _factoriesAny.createAvogadro)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.bohrRadius = /* #__PURE__ */(0, _factoriesAny.createBohrRadius)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.coulombConstant = /* #__PURE__ */(0, _factoriesAny.createCoulombConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	const divide = pureFunctionsAny_generated.divide = /* #__PURE__ */(0, _factoriesAny.createDivide)({
	  divideScalar,
	  equalScalar,
	  inv,
	  matrix,
	  multiply,
	  typed
	});
	pureFunctionsAny_generated.elementaryCharge = /* #__PURE__ */(0, _factoriesAny.createElementaryCharge)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.faraday = /* #__PURE__ */(0, _factoriesAny.createFaraday)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.freqz = /* #__PURE__ */(0, _factoriesAny.createFreqz)({
	  Complex,
	  add,
	  divide,
	  matrix,
	  multiply,
	  typed
	});
	pureFunctionsAny_generated.gravitationConstant = /* #__PURE__ */(0, _factoriesAny.createGravitationConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.inverseConductanceQuantum = /* #__PURE__ */(0, _factoriesAny.createInverseConductanceQuantum)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	const lusolve = pureFunctionsAny_generated.lusolve = /* #__PURE__ */(0, _factoriesAny.createLusolve)({
	  DenseMatrix,
	  lsolve,
	  lup,
	  matrix,
	  slu,
	  typed,
	  usolve
	});
	pureFunctionsAny_generated.magneticFluxQuantum = /* #__PURE__ */(0, _factoriesAny.createMagneticFluxQuantum)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.molarMassC12 = /* #__PURE__ */(0, _factoriesAny.createMolarMassC12)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.multinomial = /* #__PURE__ */(0, _factoriesAny.createMultinomial)({
	  add,
	  divide,
	  factorial,
	  isInteger,
	  isPositive,
	  multiply,
	  typed
	});
	pureFunctionsAny_generated.planckCharge = /* #__PURE__ */(0, _factoriesAny.createPlanckCharge)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.planckTemperature = /* #__PURE__ */(0, _factoriesAny.createPlanckTemperature)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.quantileSeq = /* #__PURE__ */(0, _factoriesAny.createQuantileSeq)({
	  bignumber,
	  add,
	  compare,
	  divide,
	  isInteger,
	  larger,
	  mapSlices,
	  multiply,
	  partitionSelect,
	  smaller,
	  smallerEq,
	  subtract,
	  typed
	});
	pureFunctionsAny_generated.rydberg = /* #__PURE__ */(0, _factoriesAny.createRydberg)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	const setIntersect = pureFunctionsAny_generated.setIntersect = /* #__PURE__ */(0, _factoriesAny.createSetIntersect)({
	  DenseMatrix,
	  Index,
	  compareNatural,
	  size,
	  subset,
	  typed
	});
	pureFunctionsAny_generated.solveODE = /* #__PURE__ */(0, _factoriesAny.createSolveODE)({
	  abs,
	  add,
	  bignumber,
	  divide,
	  isNegative,
	  isPositive,
	  larger,
	  map,
	  matrix,
	  max,
	  multiply,
	  smaller,
	  subtract,
	  typed,
	  unaryMinus
	});
	const stirlingS2 = pureFunctionsAny_generated.stirlingS2 = /* #__PURE__ */(0, _factoriesAny.createStirlingS2)({
	  bignumber,
	  addScalar,
	  combinations,
	  divideScalar,
	  factorial,
	  isInteger,
	  isNegative,
	  larger,
	  multiplyScalar,
	  number,
	  pow,
	  subtractScalar,
	  typed
	});
	pureFunctionsAny_generated.unit = /* #__PURE__ */(0, _factoriesAny.createUnitFunction)({
	  Unit,
	  typed
	});
	pureFunctionsAny_generated.bellNumbers = /* #__PURE__ */(0, _factoriesAny.createBellNumbers)({
	  addScalar,
	  isInteger,
	  isNegative,
	  stirlingS2,
	  typed
	});
	pureFunctionsAny_generated.electricConstant = /* #__PURE__ */(0, _factoriesAny.createElectricConstant)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.firstRadiation = /* #__PURE__ */(0, _factoriesAny.createFirstRadiation)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.hartreeEnergy = /* #__PURE__ */(0, _factoriesAny.createHartreeEnergy)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.kldivergence = /* #__PURE__ */(0, _factoriesAny.createKldivergence)({
	  divide,
	  dotDivide,
	  isNumeric,
	  log,
	  map,
	  matrix,
	  multiply,
	  sum,
	  typed
	});
	const mean = pureFunctionsAny_generated.mean = /* #__PURE__ */(0, _factoriesAny.createMean)({
	  add,
	  divide,
	  typed
	});
	pureFunctionsAny_generated.molarVolume = /* #__PURE__ */(0, _factoriesAny.createMolarVolume)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.planckLength = /* #__PURE__ */(0, _factoriesAny.createPlanckLength)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.setUnion = /* #__PURE__ */(0, _factoriesAny.createSetUnion)({
	  Index,
	  concat,
	  setIntersect,
	  setSymDifference,
	  size,
	  subset,
	  typed
	});
	const variance = pureFunctionsAny_generated.variance = /* #__PURE__ */(0, _factoriesAny.createVariance)({
	  add,
	  divide,
	  isNaN,
	  mapSlices,
	  multiply,
	  subtract,
	  typed
	});
	pureFunctionsAny_generated.classicalElectronRadius = /* #__PURE__ */(0, _factoriesAny.createClassicalElectronRadius)({
	  BigNumber,
	  Unit,
	  config: _configReadonly.config
	});
	pureFunctionsAny_generated.corr = /* #__PURE__ */(0, _factoriesAny.createCorr)({
	  add,
	  divide,
	  matrix,
	  mean,
	  multiply,
	  pow,
	  sqrt,
	  subtract,
	  sum,
	  typed
	});
	const median = pureFunctionsAny_generated.median = /* #__PURE__ */(0, _factoriesAny.createMedian)({
	  add,
	  compare,
	  divide,
	  partitionSelect,
	  typed
	});
	pureFunctionsAny_generated.polynomialRoot = /* #__PURE__ */(0, _factoriesAny.createPolynomialRoot)({
	  add,
	  cbrt,
	  divide,
	  equalScalar,
	  im,
	  isZero,
	  multiply,
	  re,
	  sqrt,
	  subtract,
	  typeOf,
	  typed,
	  unaryMinus
	});
	pureFunctionsAny_generated.std = /* #__PURE__ */(0, _factoriesAny.createStd)({
	  map,
	  sqrt,
	  typed,
	  variance
	});
	pureFunctionsAny_generated.zeta = /* #__PURE__ */(0, _factoriesAny.createZeta)({
	  BigNumber,
	  Complex,
	  add,
	  config: _configReadonly.config,
	  divide,
	  equal,
	  factorial,
	  gamma,
	  isBounded,
	  isNegative,
	  multiply,
	  pi,
	  pow,
	  sin,
	  smallerEq,
	  subtract,
	  typed
	});
	pureFunctionsAny_generated.mad = /* #__PURE__ */(0, _factoriesAny.createMad)({
	  abs,
	  map,
	  median,
	  subtract,
	  typed
	});
	const norm = pureFunctionsAny_generated.norm = /* #__PURE__ */(0, _factoriesAny.createNorm)({
	  abs,
	  add,
	  conj,
	  ctranspose,
	  eigs,
	  equalScalar,
	  larger,
	  matrix,
	  multiply,
	  pow,
	  smaller,
	  sqrt,
	  typed
	});
	const rotationMatrix = pureFunctionsAny_generated.rotationMatrix = /* #__PURE__ */(0, _factoriesAny.createRotationMatrix)({
	  BigNumber,
	  DenseMatrix,
	  SparseMatrix,
	  addScalar,
	  config: _configReadonly.config,
	  cos,
	  matrix,
	  multiplyScalar,
	  norm,
	  sin,
	  typed,
	  unaryMinus
	});
	pureFunctionsAny_generated.rotate = /* #__PURE__ */(0, _factoriesAny.createRotate)({
	  multiply,
	  rotationMatrix,
	  typed
	});
	const schur = pureFunctionsAny_generated.schur = /* #__PURE__ */(0, _factoriesAny.createSchur)({
	  identity,
	  matrix,
	  multiply,
	  norm,
	  qr,
	  subtract,
	  typed
	});
	const sylvester = pureFunctionsAny_generated.sylvester = /* #__PURE__ */(0, _factoriesAny.createSylvester)({
	  abs,
	  add,
	  concat,
	  identity,
	  index,
	  lusolve,
	  matrix,
	  matrixFromColumns,
	  multiply,
	  range,
	  schur,
	  subset,
	  subtract,
	  transpose,
	  typed
	});
	pureFunctionsAny_generated.lyap = /* #__PURE__ */(0, _factoriesAny.createLyap)({
	  matrix,
	  multiply,
	  sylvester,
	  transpose,
	  typed
	});
	return pureFunctionsAny_generated;
}

var impureFunctionsAny_generated = {};

var hasRequiredImpureFunctionsAny_generated;

function requireImpureFunctionsAny_generated () {
	if (hasRequiredImpureFunctionsAny_generated) return impureFunctionsAny_generated;
	hasRequiredImpureFunctionsAny_generated = 1;
	(function (exports) {

		var _interopRequireDefault = requireInteropRequireDefault();
		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		exports.derivative = exports.compile = exports.chain = exports.SymbolNode = exports.RelationalNode = exports.RangeNode = exports.Parser = exports.ParenthesisNode = exports.OperatorNode = exports.ObjectNode = exports.Node = exports.IndexNode = exports.Help = exports.FunctionNode = exports.FunctionAssignmentNode = exports.ConstantNode = exports.ConditionalNode = exports.Chain = exports.BlockNode = exports.AssignmentNode = exports.ArrayNode = exports.AccessorNode = void 0;
		Object.defineProperty(exports, "docs", {
		  enumerable: true,
		  get: function () {
		    return _embeddedDocs.embeddedDocs;
		  }
		});
		exports.symbolicEqual = exports.simplifyCore = exports.simplifyConstant = exports.simplify = exports.reviver = exports.resolve = exports.rationalize = exports.parser = exports.parse = exports.leafCount = exports.help = exports.evaluate = void 0;
		var _extends2 = _interopRequireDefault(require_extends());
		var _configReadonly = requireConfigReadonly();
		var _factoriesAny = requireFactoriesAny();
		var _pureFunctionsAnyGenerated = requirePureFunctionsAny_generated();
		var _embeddedDocs = requireEmbeddedDocs();
		/**
		 * THIS FILE IS AUTO-GENERATED
		 * DON'T MAKE CHANGES HERE
		 */

		const math = {}; // NOT pure!
		const mathWithTransform = {}; // NOT pure!
		const classes = {}; // NOT pure!

		const Node = exports.Node = (0, _factoriesAny.createNode)({
		  mathWithTransform
		});
		const ObjectNode = exports.ObjectNode = (0, _factoriesAny.createObjectNode)({
		  Node
		});
		const OperatorNode = exports.OperatorNode = (0, _factoriesAny.createOperatorNode)({
		  Node
		});
		const ParenthesisNode = exports.ParenthesisNode = (0, _factoriesAny.createParenthesisNode)({
		  Node
		});
		const RelationalNode = exports.RelationalNode = (0, _factoriesAny.createRelationalNode)({
		  Node
		});
		const ArrayNode = exports.ArrayNode = (0, _factoriesAny.createArrayNode)({
		  Node
		});
		const BlockNode = exports.BlockNode = (0, _factoriesAny.createBlockNode)({
		  Node,
		  ResultSet: _pureFunctionsAnyGenerated.ResultSet
		});
		const ConditionalNode = exports.ConditionalNode = (0, _factoriesAny.createConditionalNode)({
		  Node
		});
		const RangeNode = exports.RangeNode = (0, _factoriesAny.createRangeNode)({
		  Node
		});
		const reviver = exports.reviver = (0, _factoriesAny.createReviver)({
		  classes
		});
		const Chain = exports.Chain = (0, _factoriesAny.createChainClass)({
		  math,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const FunctionAssignmentNode = exports.FunctionAssignmentNode = (0, _factoriesAny.createFunctionAssignmentNode)({
		  Node,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const chain = exports.chain = (0, _factoriesAny.createChain)({
		  Chain,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const ConstantNode = exports.ConstantNode = (0, _factoriesAny.createConstantNode)({
		  Node,
		  isBounded: _pureFunctionsAnyGenerated.isBounded
		});
		const IndexNode = exports.IndexNode = (0, _factoriesAny.createIndexNode)({
		  Node,
		  size: _pureFunctionsAnyGenerated.size
		});
		const AccessorNode = exports.AccessorNode = (0, _factoriesAny.createAccessorNode)({
		  Node,
		  subset: _pureFunctionsAnyGenerated.subset
		});
		const AssignmentNode = exports.AssignmentNode = (0, _factoriesAny.createAssignmentNode)({
		  matrix: _pureFunctionsAnyGenerated.matrix,
		  Node,
		  subset: _pureFunctionsAnyGenerated.subset
		});
		const SymbolNode = exports.SymbolNode = (0, _factoriesAny.createSymbolNode)({
		  Unit: _pureFunctionsAnyGenerated.Unit,
		  Node,
		  math
		});
		const FunctionNode = exports.FunctionNode = (0, _factoriesAny.createFunctionNode)({
		  Node,
		  SymbolNode,
		  math
		});
		const parse = exports.parse = (0, _factoriesAny.createParse)({
		  AccessorNode,
		  ArrayNode,
		  AssignmentNode,
		  BlockNode,
		  ConditionalNode,
		  ConstantNode,
		  FunctionAssignmentNode,
		  FunctionNode,
		  IndexNode,
		  ObjectNode,
		  OperatorNode,
		  ParenthesisNode,
		  RangeNode,
		  RelationalNode,
		  SymbolNode,
		  config: _configReadonly.config,
		  numeric: _pureFunctionsAnyGenerated.numeric,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const resolve = exports.resolve = (0, _factoriesAny.createResolve)({
		  ConstantNode,
		  FunctionNode,
		  OperatorNode,
		  ParenthesisNode,
		  parse,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const simplifyConstant = exports.simplifyConstant = (0, _factoriesAny.createSimplifyConstant)({
		  bignumber: _pureFunctionsAnyGenerated.bignumber,
		  fraction: _pureFunctionsAnyGenerated.fraction,
		  AccessorNode,
		  ArrayNode,
		  ConstantNode,
		  FunctionNode,
		  IndexNode,
		  ObjectNode,
		  OperatorNode,
		  SymbolNode,
		  config: _configReadonly.config,
		  isBounded: _pureFunctionsAnyGenerated.isBounded,
		  mathWithTransform,
		  matrix: _pureFunctionsAnyGenerated.matrix,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const compile = exports.compile = (0, _factoriesAny.createCompile)({
		  parse,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const evaluate = exports.evaluate = (0, _factoriesAny.createEvaluate)({
		  parse,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const Help = exports.Help = (0, _factoriesAny.createHelpClass)({
		  evaluate
		});
		const Parser = exports.Parser = (0, _factoriesAny.createParserClass)({
		  evaluate,
		  parse
		});
		const simplifyCore = exports.simplifyCore = (0, _factoriesAny.createSimplifyCore)({
		  AccessorNode,
		  ArrayNode,
		  ConstantNode,
		  FunctionNode,
		  IndexNode,
		  ObjectNode,
		  OperatorNode,
		  ParenthesisNode,
		  SymbolNode,
		  add: _pureFunctionsAnyGenerated.add,
		  divide: _pureFunctionsAnyGenerated.divide,
		  equal: _pureFunctionsAnyGenerated.equal,
		  isZero: _pureFunctionsAnyGenerated.isZero,
		  multiply: _pureFunctionsAnyGenerated.multiply,
		  parse,
		  pow: _pureFunctionsAnyGenerated.pow,
		  subtract: _pureFunctionsAnyGenerated.subtract,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const help = exports.help = (0, _factoriesAny.createHelp)({
		  Help,
		  mathWithTransform,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const simplify = exports.simplify = (0, _factoriesAny.createSimplify)({
		  AccessorNode,
		  ArrayNode,
		  ConstantNode,
		  FunctionNode,
		  IndexNode,
		  ObjectNode,
		  OperatorNode,
		  ParenthesisNode,
		  SymbolNode,
		  equal: _pureFunctionsAnyGenerated.equal,
		  parse,
		  replacer: _pureFunctionsAnyGenerated.replacer,
		  resolve,
		  simplifyConstant,
		  simplifyCore,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const symbolicEqual = exports.symbolicEqual = (0, _factoriesAny.createSymbolicEqual)({
		  OperatorNode,
		  parse,
		  simplify,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const derivative = exports.derivative = (0, _factoriesAny.createDerivative)({
		  ConstantNode,
		  FunctionNode,
		  OperatorNode,
		  ParenthesisNode,
		  SymbolNode,
		  config: _configReadonly.config,
		  equal: _pureFunctionsAnyGenerated.equal,
		  isZero: _pureFunctionsAnyGenerated.isZero,
		  numeric: _pureFunctionsAnyGenerated.numeric,
		  parse,
		  simplify,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const parser = exports.parser = (0, _factoriesAny.createParser)({
		  Parser,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const leafCount = exports.leafCount = (0, _factoriesAny.createLeafCount)({
		  parse,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		const rationalize = exports.rationalize = (0, _factoriesAny.createRationalize)({
		  bignumber: _pureFunctionsAnyGenerated.bignumber,
		  fraction: _pureFunctionsAnyGenerated.fraction,
		  AccessorNode,
		  ArrayNode,
		  ConstantNode,
		  FunctionNode,
		  IndexNode,
		  ObjectNode,
		  OperatorNode,
		  ParenthesisNode,
		  SymbolNode,
		  add: _pureFunctionsAnyGenerated.add,
		  config: _configReadonly.config,
		  divide: _pureFunctionsAnyGenerated.divide,
		  equal: _pureFunctionsAnyGenerated.equal,
		  isZero: _pureFunctionsAnyGenerated.isZero,
		  mathWithTransform,
		  matrix: _pureFunctionsAnyGenerated.matrix,
		  multiply: _pureFunctionsAnyGenerated.multiply,
		  parse,
		  pow: _pureFunctionsAnyGenerated.pow,
		  simplify,
		  simplifyConstant,
		  simplifyCore,
		  subtract: _pureFunctionsAnyGenerated.subtract,
		  typed: _pureFunctionsAnyGenerated.typed
		});
		(0, _extends2.default)(math, {
		  e: _pureFunctionsAnyGenerated.e,
		  false: _pureFunctionsAnyGenerated._false,
		  fineStructure: _pureFunctionsAnyGenerated.fineStructure,
		  i: _pureFunctionsAnyGenerated.i,
		  Infinity: _pureFunctionsAnyGenerated._Infinity,
		  LN10: _pureFunctionsAnyGenerated.LN10,
		  LOG10E: _pureFunctionsAnyGenerated.LOG10E,
		  NaN: _pureFunctionsAnyGenerated._NaN,
		  null: _pureFunctionsAnyGenerated._null,
		  phi: _pureFunctionsAnyGenerated.phi,
		  SQRT1_2: _pureFunctionsAnyGenerated.SQRT1_2,
		  sackurTetrode: _pureFunctionsAnyGenerated.sackurTetrode,
		  tau: _pureFunctionsAnyGenerated.tau,
		  true: _pureFunctionsAnyGenerated._true,
		  'E': _pureFunctionsAnyGenerated.e,
		  version: _pureFunctionsAnyGenerated.version,
		  efimovFactor: _pureFunctionsAnyGenerated.efimovFactor,
		  LN2: _pureFunctionsAnyGenerated.LN2,
		  pi: _pureFunctionsAnyGenerated.pi,
		  replacer: _pureFunctionsAnyGenerated.replacer,
		  reviver,
		  SQRT2: _pureFunctionsAnyGenerated.SQRT2,
		  typed: _pureFunctionsAnyGenerated.typed,
		  'PI': _pureFunctionsAnyGenerated.pi,
		  weakMixingAngle: _pureFunctionsAnyGenerated.weakMixingAngle,
		  abs: _pureFunctionsAnyGenerated.abs,
		  acos: _pureFunctionsAnyGenerated.acos,
		  acot: _pureFunctionsAnyGenerated.acot,
		  acsc: _pureFunctionsAnyGenerated.acsc,
		  addScalar: _pureFunctionsAnyGenerated.addScalar,
		  arg: _pureFunctionsAnyGenerated.arg,
		  asech: _pureFunctionsAnyGenerated.asech,
		  asinh: _pureFunctionsAnyGenerated.asinh,
		  atan: _pureFunctionsAnyGenerated.atan,
		  atanh: _pureFunctionsAnyGenerated.atanh,
		  bigint: _pureFunctionsAnyGenerated.bigint,
		  bitNot: _pureFunctionsAnyGenerated.bitNot,
		  boolean: _pureFunctionsAnyGenerated.boolean,
		  clone: _pureFunctionsAnyGenerated.clone,
		  combinations: _pureFunctionsAnyGenerated.combinations,
		  complex: _pureFunctionsAnyGenerated.complex,
		  conj: _pureFunctionsAnyGenerated.conj,
		  cos: _pureFunctionsAnyGenerated.cos,
		  cot: _pureFunctionsAnyGenerated.cot,
		  csc: _pureFunctionsAnyGenerated.csc,
		  cube: _pureFunctionsAnyGenerated.cube,
		  equalScalar: _pureFunctionsAnyGenerated.equalScalar,
		  erf: _pureFunctionsAnyGenerated.erf,
		  exp: _pureFunctionsAnyGenerated.exp,
		  expm1: _pureFunctionsAnyGenerated.expm1,
		  filter: _pureFunctionsAnyGenerated.filter,
		  flatten: _pureFunctionsAnyGenerated.flatten,
		  forEach: _pureFunctionsAnyGenerated.forEach,
		  format: _pureFunctionsAnyGenerated.format,
		  getMatrixDataType: _pureFunctionsAnyGenerated.getMatrixDataType,
		  hex: _pureFunctionsAnyGenerated.hex,
		  im: _pureFunctionsAnyGenerated.im,
		  isBounded: _pureFunctionsAnyGenerated.isBounded,
		  isNaN: _pureFunctionsAnyGenerated.isNaN,
		  isNumeric: _pureFunctionsAnyGenerated.isNumeric,
		  isPrime: _pureFunctionsAnyGenerated.isPrime,
		  LOG2E: _pureFunctionsAnyGenerated.LOG2E,
		  lgamma: _pureFunctionsAnyGenerated.lgamma,
		  log10: _pureFunctionsAnyGenerated.log10,
		  log2: _pureFunctionsAnyGenerated.log2,
		  map: _pureFunctionsAnyGenerated.map,
		  mode: _pureFunctionsAnyGenerated.mode,
		  multiplyScalar: _pureFunctionsAnyGenerated.multiplyScalar,
		  not: _pureFunctionsAnyGenerated.not,
		  number: _pureFunctionsAnyGenerated.number,
		  oct: _pureFunctionsAnyGenerated.oct,
		  pickRandom: _pureFunctionsAnyGenerated.pickRandom,
		  print: _pureFunctionsAnyGenerated.print,
		  random: _pureFunctionsAnyGenerated.random,
		  re: _pureFunctionsAnyGenerated.re,
		  sec: _pureFunctionsAnyGenerated.sec,
		  sign: _pureFunctionsAnyGenerated.sign,
		  sin: _pureFunctionsAnyGenerated.sin,
		  size: _pureFunctionsAnyGenerated.size,
		  splitUnit: _pureFunctionsAnyGenerated.splitUnit,
		  square: _pureFunctionsAnyGenerated.square,
		  string: _pureFunctionsAnyGenerated.string,
		  subtractScalar: _pureFunctionsAnyGenerated.subtractScalar,
		  tan: _pureFunctionsAnyGenerated.tan,
		  toBest: _pureFunctionsAnyGenerated.toBest,
		  typeOf: _pureFunctionsAnyGenerated.typeOf,
		  acosh: _pureFunctionsAnyGenerated.acosh,
		  acsch: _pureFunctionsAnyGenerated.acsch,
		  asec: _pureFunctionsAnyGenerated.asec,
		  bignumber: _pureFunctionsAnyGenerated.bignumber,
		  chain,
		  combinationsWithRep: _pureFunctionsAnyGenerated.combinationsWithRep,
		  cosh: _pureFunctionsAnyGenerated.cosh,
		  csch: _pureFunctionsAnyGenerated.csch,
		  dot: _pureFunctionsAnyGenerated.dot,
		  hasNumericValue: _pureFunctionsAnyGenerated.hasNumericValue,
		  isFinite: _pureFunctionsAnyGenerated.isFinite,
		  isNegative: _pureFunctionsAnyGenerated.isNegative,
		  isZero: _pureFunctionsAnyGenerated.isZero,
		  matrix: _pureFunctionsAnyGenerated.matrix,
		  matrixFromFunction: _pureFunctionsAnyGenerated.matrixFromFunction,
		  multiply: _pureFunctionsAnyGenerated.multiply,
		  ones: _pureFunctionsAnyGenerated.ones,
		  randomInt: _pureFunctionsAnyGenerated.randomInt,
		  resize: _pureFunctionsAnyGenerated.resize,
		  sech: _pureFunctionsAnyGenerated.sech,
		  sinh: _pureFunctionsAnyGenerated.sinh,
		  sparse: _pureFunctionsAnyGenerated.sparse,
		  sqrt: _pureFunctionsAnyGenerated.sqrt,
		  squeeze: _pureFunctionsAnyGenerated.squeeze,
		  tanh: _pureFunctionsAnyGenerated.tanh,
		  transpose: _pureFunctionsAnyGenerated.transpose,
		  xgcd: _pureFunctionsAnyGenerated.xgcd,
		  zeros: _pureFunctionsAnyGenerated.zeros,
		  acoth: _pureFunctionsAnyGenerated.acoth,
		  asin: _pureFunctionsAnyGenerated.asin,
		  bin: _pureFunctionsAnyGenerated.bin,
		  coth: _pureFunctionsAnyGenerated.coth,
		  ctranspose: _pureFunctionsAnyGenerated.ctranspose,
		  diag: _pureFunctionsAnyGenerated.diag,
		  equal: _pureFunctionsAnyGenerated.equal,
		  fraction: _pureFunctionsAnyGenerated.fraction,
		  identity: _pureFunctionsAnyGenerated.identity,
		  isInteger: _pureFunctionsAnyGenerated.isInteger,
		  kron: _pureFunctionsAnyGenerated.kron,
		  mapSlices: _pureFunctionsAnyGenerated.mapSlices,
		  matrixFromColumns: _pureFunctionsAnyGenerated.matrixFromColumns,
		  num: _pureFunctionsAnyGenerated.num,
		  reshape: _pureFunctionsAnyGenerated.reshape,
		  round: _pureFunctionsAnyGenerated.round,
		  unaryMinus: _pureFunctionsAnyGenerated.unaryMinus,
		  bernoulli: _pureFunctionsAnyGenerated.bernoulli,
		  cbrt: _pureFunctionsAnyGenerated.cbrt,
		  concat: _pureFunctionsAnyGenerated.concat,
		  deepEqual: _pureFunctionsAnyGenerated.deepEqual,
		  dotMultiply: _pureFunctionsAnyGenerated.dotMultiply,
		  floor: _pureFunctionsAnyGenerated.floor,
		  gcd: _pureFunctionsAnyGenerated.gcd,
		  isPositive: _pureFunctionsAnyGenerated.isPositive,
		  larger: _pureFunctionsAnyGenerated.larger,
		  lcm: _pureFunctionsAnyGenerated.lcm,
		  leftShift: _pureFunctionsAnyGenerated.leftShift,
		  mod: _pureFunctionsAnyGenerated.mod,
		  nthRoot: _pureFunctionsAnyGenerated.nthRoot,
		  nullish: _pureFunctionsAnyGenerated.nullish,
		  numeric: _pureFunctionsAnyGenerated.numeric,
		  prod: _pureFunctionsAnyGenerated.prod,
		  rightArithShift: _pureFunctionsAnyGenerated.rightArithShift,
		  smaller: _pureFunctionsAnyGenerated.smaller,
		  subtract: _pureFunctionsAnyGenerated.subtract,
		  to: _pureFunctionsAnyGenerated.to,
		  unaryPlus: _pureFunctionsAnyGenerated.unaryPlus,
		  xor: _pureFunctionsAnyGenerated.xor,
		  add: _pureFunctionsAnyGenerated.add,
		  atan2: _pureFunctionsAnyGenerated.atan2,
		  bitAnd: _pureFunctionsAnyGenerated.bitAnd,
		  bitOr: _pureFunctionsAnyGenerated.bitOr,
		  bitXor: _pureFunctionsAnyGenerated.bitXor,
		  ceil: _pureFunctionsAnyGenerated.ceil,
		  compare: _pureFunctionsAnyGenerated.compare,
		  compareText: _pureFunctionsAnyGenerated.compareText,
		  composition: _pureFunctionsAnyGenerated.composition,
		  count: _pureFunctionsAnyGenerated.count,
		  cross: _pureFunctionsAnyGenerated.cross,
		  diff: _pureFunctionsAnyGenerated.diff,
		  divideScalar: _pureFunctionsAnyGenerated.divideScalar,
		  equalText: _pureFunctionsAnyGenerated.equalText,
		  fix: _pureFunctionsAnyGenerated.fix,
		  hypot: _pureFunctionsAnyGenerated.hypot,
		  intersect: _pureFunctionsAnyGenerated.intersect,
		  invmod: _pureFunctionsAnyGenerated.invmod,
		  largerEq: _pureFunctionsAnyGenerated.largerEq,
		  log: _pureFunctionsAnyGenerated.log,
		  lsolve: _pureFunctionsAnyGenerated.lsolve,
		  matrixFromRows: _pureFunctionsAnyGenerated.matrixFromRows,
		  min: _pureFunctionsAnyGenerated.min,
		  nthRoots: _pureFunctionsAnyGenerated.nthRoots,
		  partitionSelect: _pureFunctionsAnyGenerated.partitionSelect,
		  qr: _pureFunctionsAnyGenerated.qr,
		  rightLogShift: _pureFunctionsAnyGenerated.rightLogShift,
		  slu: _pureFunctionsAnyGenerated.slu,
		  subset: _pureFunctionsAnyGenerated.subset,
		  sum: _pureFunctionsAnyGenerated.sum,
		  trace: _pureFunctionsAnyGenerated.trace,
		  usolve: _pureFunctionsAnyGenerated.usolve,
		  zpk2tf: _pureFunctionsAnyGenerated.zpk2tf,
		  catalan: _pureFunctionsAnyGenerated.catalan,
		  compareNatural: _pureFunctionsAnyGenerated.compareNatural,
		  cumsum: _pureFunctionsAnyGenerated.cumsum,
		  det: _pureFunctionsAnyGenerated.det,
		  dotDivide: _pureFunctionsAnyGenerated.dotDivide,
		  index: _pureFunctionsAnyGenerated.index,
		  inv: _pureFunctionsAnyGenerated.inv,
		  log1p: _pureFunctionsAnyGenerated.log1p,
		  lsolveAll: _pureFunctionsAnyGenerated.lsolveAll,
		  max: _pureFunctionsAnyGenerated.max,
		  or: _pureFunctionsAnyGenerated.or,
		  pinv: _pureFunctionsAnyGenerated.pinv,
		  pow: _pureFunctionsAnyGenerated.pow,
		  setCartesian: _pureFunctionsAnyGenerated.setCartesian,
		  setDistinct: _pureFunctionsAnyGenerated.setDistinct,
		  setIsSubset: _pureFunctionsAnyGenerated.setIsSubset,
		  setPowerset: _pureFunctionsAnyGenerated.setPowerset,
		  smallerEq: _pureFunctionsAnyGenerated.smallerEq,
		  sort: _pureFunctionsAnyGenerated.sort,
		  sqrtm: _pureFunctionsAnyGenerated.sqrtm,
		  unequal: _pureFunctionsAnyGenerated.unequal,
		  usolveAll: _pureFunctionsAnyGenerated.usolveAll,
		  and: _pureFunctionsAnyGenerated.and,
		  den: _pureFunctionsAnyGenerated.den,
		  distance: _pureFunctionsAnyGenerated.distance,
		  dotPow: _pureFunctionsAnyGenerated.dotPow,
		  expm: _pureFunctionsAnyGenerated.expm,
		  fft: _pureFunctionsAnyGenerated.fft,
		  gamma: _pureFunctionsAnyGenerated.gamma,
		  ifft: _pureFunctionsAnyGenerated.ifft,
		  lup: _pureFunctionsAnyGenerated.lup,
		  range: _pureFunctionsAnyGenerated.range,
		  row: _pureFunctionsAnyGenerated.row,
		  setDifference: _pureFunctionsAnyGenerated.setDifference,
		  setMultiplicity: _pureFunctionsAnyGenerated.setMultiplicity,
		  setSymDifference: _pureFunctionsAnyGenerated.setSymDifference,
		  vacuumImpedance: _pureFunctionsAnyGenerated.vacuumImpedance,
		  wienDisplacement: _pureFunctionsAnyGenerated.wienDisplacement,
		  atomicMass: _pureFunctionsAnyGenerated.atomicMass,
		  bohrMagneton: _pureFunctionsAnyGenerated.bohrMagneton,
		  boltzmann: _pureFunctionsAnyGenerated.boltzmann,
		  column: _pureFunctionsAnyGenerated.column,
		  conductanceQuantum: _pureFunctionsAnyGenerated.conductanceQuantum,
		  coulomb: _pureFunctionsAnyGenerated.coulomb,
		  createUnit: _pureFunctionsAnyGenerated.createUnit,
		  deuteronMass: _pureFunctionsAnyGenerated.deuteronMass,
		  eigs: _pureFunctionsAnyGenerated.eigs,
		  electronMass: _pureFunctionsAnyGenerated.electronMass,
		  factorial: _pureFunctionsAnyGenerated.factorial,
		  fermiCoupling: _pureFunctionsAnyGenerated.fermiCoupling,
		  gasConstant: _pureFunctionsAnyGenerated.gasConstant,
		  gravity: _pureFunctionsAnyGenerated.gravity,
		  klitzing: _pureFunctionsAnyGenerated.klitzing,
		  loschmidt: _pureFunctionsAnyGenerated.loschmidt,
		  magneticConstant: _pureFunctionsAnyGenerated.magneticConstant,
		  molarMass: _pureFunctionsAnyGenerated.molarMass,
		  molarPlanckConstant: _pureFunctionsAnyGenerated.molarPlanckConstant,
		  neutronMass: _pureFunctionsAnyGenerated.neutronMass,
		  nuclearMagneton: _pureFunctionsAnyGenerated.nuclearMagneton,
		  permutations: _pureFunctionsAnyGenerated.permutations,
		  planckConstant: _pureFunctionsAnyGenerated.planckConstant,
		  planckMass: _pureFunctionsAnyGenerated.planckMass,
		  planckTime: _pureFunctionsAnyGenerated.planckTime,
		  protonMass: _pureFunctionsAnyGenerated.protonMass,
		  quantumOfCirculation: _pureFunctionsAnyGenerated.quantumOfCirculation,
		  reducedPlanckConstant: _pureFunctionsAnyGenerated.reducedPlanckConstant,
		  secondRadiation: _pureFunctionsAnyGenerated.secondRadiation,
		  setSize: _pureFunctionsAnyGenerated.setSize,
		  speedOfLight: _pureFunctionsAnyGenerated.speedOfLight,
		  stefanBoltzmann: _pureFunctionsAnyGenerated.stefanBoltzmann,
		  thomsonCrossSection: _pureFunctionsAnyGenerated.thomsonCrossSection,
		  avogadro: _pureFunctionsAnyGenerated.avogadro,
		  bohrRadius: _pureFunctionsAnyGenerated.bohrRadius,
		  coulombConstant: _pureFunctionsAnyGenerated.coulombConstant,
		  divide: _pureFunctionsAnyGenerated.divide,
		  elementaryCharge: _pureFunctionsAnyGenerated.elementaryCharge,
		  faraday: _pureFunctionsAnyGenerated.faraday,
		  freqz: _pureFunctionsAnyGenerated.freqz,
		  gravitationConstant: _pureFunctionsAnyGenerated.gravitationConstant,
		  inverseConductanceQuantum: _pureFunctionsAnyGenerated.inverseConductanceQuantum,
		  lusolve: _pureFunctionsAnyGenerated.lusolve,
		  magneticFluxQuantum: _pureFunctionsAnyGenerated.magneticFluxQuantum,
		  molarMassC12: _pureFunctionsAnyGenerated.molarMassC12,
		  multinomial: _pureFunctionsAnyGenerated.multinomial,
		  planckCharge: _pureFunctionsAnyGenerated.planckCharge,
		  planckTemperature: _pureFunctionsAnyGenerated.planckTemperature,
		  quantileSeq: _pureFunctionsAnyGenerated.quantileSeq,
		  rydberg: _pureFunctionsAnyGenerated.rydberg,
		  setIntersect: _pureFunctionsAnyGenerated.setIntersect,
		  solveODE: _pureFunctionsAnyGenerated.solveODE,
		  stirlingS2: _pureFunctionsAnyGenerated.stirlingS2,
		  unit: _pureFunctionsAnyGenerated.unit,
		  bellNumbers: _pureFunctionsAnyGenerated.bellNumbers,
		  electricConstant: _pureFunctionsAnyGenerated.electricConstant,
		  firstRadiation: _pureFunctionsAnyGenerated.firstRadiation,
		  hartreeEnergy: _pureFunctionsAnyGenerated.hartreeEnergy,
		  kldivergence: _pureFunctionsAnyGenerated.kldivergence,
		  mean: _pureFunctionsAnyGenerated.mean,
		  molarVolume: _pureFunctionsAnyGenerated.molarVolume,
		  planckLength: _pureFunctionsAnyGenerated.planckLength,
		  setUnion: _pureFunctionsAnyGenerated.setUnion,
		  variance: _pureFunctionsAnyGenerated.variance,
		  classicalElectronRadius: _pureFunctionsAnyGenerated.classicalElectronRadius,
		  corr: _pureFunctionsAnyGenerated.corr,
		  median: _pureFunctionsAnyGenerated.median,
		  parse,
		  polynomialRoot: _pureFunctionsAnyGenerated.polynomialRoot,
		  resolve,
		  simplifyConstant,
		  std: _pureFunctionsAnyGenerated.std,
		  zeta: _pureFunctionsAnyGenerated.zeta,
		  compile,
		  evaluate,
		  mad: _pureFunctionsAnyGenerated.mad,
		  simplifyCore,
		  help,
		  norm: _pureFunctionsAnyGenerated.norm,
		  rotationMatrix: _pureFunctionsAnyGenerated.rotationMatrix,
		  simplify,
		  symbolicEqual,
		  derivative,
		  parser,
		  rotate: _pureFunctionsAnyGenerated.rotate,
		  leafCount,
		  rationalize,
		  schur: _pureFunctionsAnyGenerated.schur,
		  sylvester: _pureFunctionsAnyGenerated.sylvester,
		  lyap: _pureFunctionsAnyGenerated.lyap,
		  config: _configReadonly.config
		});
		(0, _extends2.default)(mathWithTransform, math, {
		  map: (0, _factoriesAny.createMapTransform)({
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  filter: (0, _factoriesAny.createFilterTransform)({
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  forEach: (0, _factoriesAny.createForEachTransform)({
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  mapSlices: (0, _factoriesAny.createMapSlicesTransform)({
		    isInteger: _pureFunctionsAnyGenerated.isInteger,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  or: (0, _factoriesAny.createOrTransform)({
		    DenseMatrix: _pureFunctionsAnyGenerated.DenseMatrix,
		    concat: _pureFunctionsAnyGenerated.concat,
		    equalScalar: _pureFunctionsAnyGenerated.equalScalar,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  and: (0, _factoriesAny.createAndTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    concat: _pureFunctionsAnyGenerated.concat,
		    equalScalar: _pureFunctionsAnyGenerated.equalScalar,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    not: _pureFunctionsAnyGenerated.not,
		    typed: _pureFunctionsAnyGenerated.typed,
		    zeros: _pureFunctionsAnyGenerated.zeros
		  }),
		  cumsum: (0, _factoriesAny.createCumSumTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    typed: _pureFunctionsAnyGenerated.typed,
		    unaryPlus: _pureFunctionsAnyGenerated.unaryPlus
		  }),
		  max: (0, _factoriesAny.createMaxTransform)({
		    config: _configReadonly.config,
		    isNaN: _pureFunctionsAnyGenerated.isNaN,
		    larger: _pureFunctionsAnyGenerated.larger,
		    numeric: _pureFunctionsAnyGenerated.numeric,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  nullish: (0, _factoriesAny.createNullishTransform)({
		    deepEqual: _pureFunctionsAnyGenerated.deepEqual,
		    flatten: _pureFunctionsAnyGenerated.flatten,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    size: _pureFunctionsAnyGenerated.size,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  print: (0, _factoriesAny.createPrintTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    typed: _pureFunctionsAnyGenerated.typed,
		    zeros: _pureFunctionsAnyGenerated.zeros
		  }),
		  bitAnd: (0, _factoriesAny.createBitAndTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    concat: _pureFunctionsAnyGenerated.concat,
		    equalScalar: _pureFunctionsAnyGenerated.equalScalar,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    not: _pureFunctionsAnyGenerated.not,
		    typed: _pureFunctionsAnyGenerated.typed,
		    zeros: _pureFunctionsAnyGenerated.zeros
		  }),
		  concat: (0, _factoriesAny.createConcatTransform)({
		    isInteger: _pureFunctionsAnyGenerated.isInteger,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  diff: (0, _factoriesAny.createDiffTransform)({
		    bignumber: _pureFunctionsAnyGenerated.bignumber,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    number: _pureFunctionsAnyGenerated.number,
		    subtract: _pureFunctionsAnyGenerated.subtract,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  min: (0, _factoriesAny.createMinTransform)({
		    config: _configReadonly.config,
		    isNaN: _pureFunctionsAnyGenerated.isNaN,
		    numeric: _pureFunctionsAnyGenerated.numeric,
		    smaller: _pureFunctionsAnyGenerated.smaller,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  subset: (0, _factoriesAny.createSubsetTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    typed: _pureFunctionsAnyGenerated.typed,
		    zeros: _pureFunctionsAnyGenerated.zeros
		  }),
		  bitOr: (0, _factoriesAny.createBitOrTransform)({
		    DenseMatrix: _pureFunctionsAnyGenerated.DenseMatrix,
		    concat: _pureFunctionsAnyGenerated.concat,
		    equalScalar: _pureFunctionsAnyGenerated.equalScalar,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  sum: (0, _factoriesAny.createSumTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    config: _configReadonly.config,
		    numeric: _pureFunctionsAnyGenerated.numeric,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  index: (0, _factoriesAny.createIndexTransform)({
		    Index: _pureFunctionsAnyGenerated.Index,
		    getMatrixDataType: _pureFunctionsAnyGenerated.getMatrixDataType
		  }),
		  row: (0, _factoriesAny.createRowTransform)({
		    Index: _pureFunctionsAnyGenerated.Index,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    range: _pureFunctionsAnyGenerated.range,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  column: (0, _factoriesAny.createColumnTransform)({
		    Index: _pureFunctionsAnyGenerated.Index,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    range: _pureFunctionsAnyGenerated.range,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  mean: (0, _factoriesAny.createMeanTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    divide: _pureFunctionsAnyGenerated.divide,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  range: (0, _factoriesAny.createRangeTransform)({
		    bignumber: _pureFunctionsAnyGenerated.bignumber,
		    matrix: _pureFunctionsAnyGenerated.matrix,
		    add: _pureFunctionsAnyGenerated.add,
		    config: _configReadonly.config,
		    equal: _pureFunctionsAnyGenerated.equal,
		    isPositive: _pureFunctionsAnyGenerated.isPositive,
		    isZero: _pureFunctionsAnyGenerated.isZero,
		    larger: _pureFunctionsAnyGenerated.larger,
		    largerEq: _pureFunctionsAnyGenerated.largerEq,
		    smaller: _pureFunctionsAnyGenerated.smaller,
		    smallerEq: _pureFunctionsAnyGenerated.smallerEq,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  variance: (0, _factoriesAny.createVarianceTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    divide: _pureFunctionsAnyGenerated.divide,
		    isNaN: _pureFunctionsAnyGenerated.isNaN,
		    mapSlices: _pureFunctionsAnyGenerated.mapSlices,
		    multiply: _pureFunctionsAnyGenerated.multiply,
		    subtract: _pureFunctionsAnyGenerated.subtract,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  quantileSeq: (0, _factoriesAny.createQuantileSeqTransform)({
		    add: _pureFunctionsAnyGenerated.add,
		    bignumber: _pureFunctionsAnyGenerated.bignumber,
		    compare: _pureFunctionsAnyGenerated.compare,
		    divide: _pureFunctionsAnyGenerated.divide,
		    isInteger: _pureFunctionsAnyGenerated.isInteger,
		    larger: _pureFunctionsAnyGenerated.larger,
		    mapSlices: _pureFunctionsAnyGenerated.mapSlices,
		    multiply: _pureFunctionsAnyGenerated.multiply,
		    partitionSelect: _pureFunctionsAnyGenerated.partitionSelect,
		    smaller: _pureFunctionsAnyGenerated.smaller,
		    smallerEq: _pureFunctionsAnyGenerated.smallerEq,
		    subtract: _pureFunctionsAnyGenerated.subtract,
		    typed: _pureFunctionsAnyGenerated.typed
		  }),
		  std: (0, _factoriesAny.createStdTransform)({
		    map: _pureFunctionsAnyGenerated.map,
		    sqrt: _pureFunctionsAnyGenerated.sqrt,
		    typed: _pureFunctionsAnyGenerated.typed,
		    variance: _pureFunctionsAnyGenerated.variance
		  })
		});
		(0, _extends2.default)(classes, {
		  BigNumber: _pureFunctionsAnyGenerated.BigNumber,
		  Complex: _pureFunctionsAnyGenerated.Complex,
		  Fraction: _pureFunctionsAnyGenerated.Fraction,
		  Matrix: _pureFunctionsAnyGenerated.Matrix,
		  Node,
		  ObjectNode,
		  OperatorNode,
		  ParenthesisNode,
		  Range: _pureFunctionsAnyGenerated.Range,
		  RelationalNode,
		  ResultSet: _pureFunctionsAnyGenerated.ResultSet,
		  ArrayNode,
		  BlockNode,
		  ConditionalNode,
		  DenseMatrix: _pureFunctionsAnyGenerated.DenseMatrix,
		  RangeNode,
		  Chain,
		  FunctionAssignmentNode,
		  SparseMatrix: _pureFunctionsAnyGenerated.SparseMatrix,
		  ConstantNode,
		  IndexNode,
		  FibonacciHeap: _pureFunctionsAnyGenerated.FibonacciHeap,
		  ImmutableDenseMatrix: _pureFunctionsAnyGenerated.ImmutableDenseMatrix,
		  Index: _pureFunctionsAnyGenerated.Index,
		  Spa: _pureFunctionsAnyGenerated.Spa,
		  AccessorNode,
		  AssignmentNode,
		  Unit: _pureFunctionsAnyGenerated.Unit,
		  SymbolNode,
		  FunctionNode,
		  Help,
		  Parser
		});
		Chain.createProxy(math); 
	} (impureFunctionsAny_generated));
	return impureFunctionsAny_generated;
}

var typeChecks = {};

var hasRequiredTypeChecks;

function requireTypeChecks () {
	if (hasRequiredTypeChecks) return typeChecks;
	hasRequiredTypeChecks = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		Object.defineProperty(exports, "isAccessorNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isAccessorNode;
		  }
		});
		Object.defineProperty(exports, "isArray", {
		  enumerable: true,
		  get: function () {
		    return _is.isArray;
		  }
		});
		Object.defineProperty(exports, "isArrayNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isArrayNode;
		  }
		});
		Object.defineProperty(exports, "isAssignmentNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isAssignmentNode;
		  }
		});
		Object.defineProperty(exports, "isBigInt", {
		  enumerable: true,
		  get: function () {
		    return _is.isBigInt;
		  }
		});
		Object.defineProperty(exports, "isBigNumber", {
		  enumerable: true,
		  get: function () {
		    return _is.isBigNumber;
		  }
		});
		Object.defineProperty(exports, "isBlockNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isBlockNode;
		  }
		});
		Object.defineProperty(exports, "isBoolean", {
		  enumerable: true,
		  get: function () {
		    return _is.isBoolean;
		  }
		});
		Object.defineProperty(exports, "isChain", {
		  enumerable: true,
		  get: function () {
		    return _is.isChain;
		  }
		});
		Object.defineProperty(exports, "isCollection", {
		  enumerable: true,
		  get: function () {
		    return _is.isCollection;
		  }
		});
		Object.defineProperty(exports, "isComplex", {
		  enumerable: true,
		  get: function () {
		    return _is.isComplex;
		  }
		});
		Object.defineProperty(exports, "isConditionalNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isConditionalNode;
		  }
		});
		Object.defineProperty(exports, "isConstantNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isConstantNode;
		  }
		});
		Object.defineProperty(exports, "isDate", {
		  enumerable: true,
		  get: function () {
		    return _is.isDate;
		  }
		});
		Object.defineProperty(exports, "isDenseMatrix", {
		  enumerable: true,
		  get: function () {
		    return _is.isDenseMatrix;
		  }
		});
		Object.defineProperty(exports, "isFraction", {
		  enumerable: true,
		  get: function () {
		    return _is.isFraction;
		  }
		});
		Object.defineProperty(exports, "isFunction", {
		  enumerable: true,
		  get: function () {
		    return _is.isFunction;
		  }
		});
		Object.defineProperty(exports, "isFunctionAssignmentNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isFunctionAssignmentNode;
		  }
		});
		Object.defineProperty(exports, "isFunctionNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isFunctionNode;
		  }
		});
		Object.defineProperty(exports, "isHelp", {
		  enumerable: true,
		  get: function () {
		    return _is.isHelp;
		  }
		});
		Object.defineProperty(exports, "isIndex", {
		  enumerable: true,
		  get: function () {
		    return _is.isIndex;
		  }
		});
		Object.defineProperty(exports, "isIndexNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isIndexNode;
		  }
		});
		Object.defineProperty(exports, "isMap", {
		  enumerable: true,
		  get: function () {
		    return _is.isMap;
		  }
		});
		Object.defineProperty(exports, "isMatrix", {
		  enumerable: true,
		  get: function () {
		    return _is.isMatrix;
		  }
		});
		Object.defineProperty(exports, "isNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isNode;
		  }
		});
		Object.defineProperty(exports, "isNull", {
		  enumerable: true,
		  get: function () {
		    return _is.isNull;
		  }
		});
		Object.defineProperty(exports, "isNumber", {
		  enumerable: true,
		  get: function () {
		    return _is.isNumber;
		  }
		});
		Object.defineProperty(exports, "isObject", {
		  enumerable: true,
		  get: function () {
		    return _is.isObject;
		  }
		});
		Object.defineProperty(exports, "isObjectNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isObjectNode;
		  }
		});
		Object.defineProperty(exports, "isObjectWrappingMap", {
		  enumerable: true,
		  get: function () {
		    return _is.isObjectWrappingMap;
		  }
		});
		Object.defineProperty(exports, "isOperatorNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isOperatorNode;
		  }
		});
		Object.defineProperty(exports, "isParenthesisNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isParenthesisNode;
		  }
		});
		Object.defineProperty(exports, "isPartitionedMap", {
		  enumerable: true,
		  get: function () {
		    return _is.isPartitionedMap;
		  }
		});
		Object.defineProperty(exports, "isRange", {
		  enumerable: true,
		  get: function () {
		    return _is.isRange;
		  }
		});
		Object.defineProperty(exports, "isRangeNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isRangeNode;
		  }
		});
		Object.defineProperty(exports, "isRegExp", {
		  enumerable: true,
		  get: function () {
		    return _is.isRegExp;
		  }
		});
		Object.defineProperty(exports, "isRelationalNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isRelationalNode;
		  }
		});
		Object.defineProperty(exports, "isResultSet", {
		  enumerable: true,
		  get: function () {
		    return _is.isResultSet;
		  }
		});
		Object.defineProperty(exports, "isSparseMatrix", {
		  enumerable: true,
		  get: function () {
		    return _is.isSparseMatrix;
		  }
		});
		Object.defineProperty(exports, "isString", {
		  enumerable: true,
		  get: function () {
		    return _is.isString;
		  }
		});
		Object.defineProperty(exports, "isSymbolNode", {
		  enumerable: true,
		  get: function () {
		    return _is.isSymbolNode;
		  }
		});
		Object.defineProperty(exports, "isUndefined", {
		  enumerable: true,
		  get: function () {
		    return _is.isUndefined;
		  }
		});
		Object.defineProperty(exports, "isUnit", {
		  enumerable: true,
		  get: function () {
		    return _is.isUnit;
		  }
		});
		var _is = requireIs(); 
	} (typeChecks));
	return typeChecks;
}

var dependenciesAny_generated = {};

var dependenciesAbs_generated = {};

var dependenciesTyped_generated = {};

var dependenciesBigNumberClass_generated = {};

var hasRequiredDependenciesBigNumberClass_generated;

function requireDependenciesBigNumberClass_generated () {
	if (hasRequiredDependenciesBigNumberClass_generated) return dependenciesBigNumberClass_generated;
	hasRequiredDependenciesBigNumberClass_generated = 1;

	Object.defineProperty(dependenciesBigNumberClass_generated, "__esModule", {
	  value: true
	});
	dependenciesBigNumberClass_generated.BigNumberDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBigNumberClass_generated.BigNumberDependencies = {
	  createBigNumberClass: _factoriesAny.createBigNumberClass
	};
	return dependenciesBigNumberClass_generated;
}

var dependenciesComplexClass_generated = {};

var hasRequiredDependenciesComplexClass_generated;

function requireDependenciesComplexClass_generated () {
	if (hasRequiredDependenciesComplexClass_generated) return dependenciesComplexClass_generated;
	hasRequiredDependenciesComplexClass_generated = 1;

	Object.defineProperty(dependenciesComplexClass_generated, "__esModule", {
	  value: true
	});
	dependenciesComplexClass_generated.ComplexDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesComplexClass_generated.ComplexDependencies = {
	  createComplexClass: _factoriesAny.createComplexClass
	};
	return dependenciesComplexClass_generated;
}

var dependenciesDenseMatrixClass_generated = {};

var dependenciesMatrixClass_generated = {};

var hasRequiredDependenciesMatrixClass_generated;

function requireDependenciesMatrixClass_generated () {
	if (hasRequiredDependenciesMatrixClass_generated) return dependenciesMatrixClass_generated;
	hasRequiredDependenciesMatrixClass_generated = 1;

	Object.defineProperty(dependenciesMatrixClass_generated, "__esModule", {
	  value: true
	});
	dependenciesMatrixClass_generated.MatrixDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMatrixClass_generated.MatrixDependencies = {
	  createMatrixClass: _factoriesAny.createMatrixClass
	};
	return dependenciesMatrixClass_generated;
}

var hasRequiredDependenciesDenseMatrixClass_generated;

function requireDependenciesDenseMatrixClass_generated () {
	if (hasRequiredDependenciesDenseMatrixClass_generated) return dependenciesDenseMatrixClass_generated;
	hasRequiredDependenciesDenseMatrixClass_generated = 1;

	Object.defineProperty(dependenciesDenseMatrixClass_generated, "__esModule", {
	  value: true
	});
	dependenciesDenseMatrixClass_generated.DenseMatrixDependencies = void 0;
	var _dependenciesMatrixClassGenerated = requireDependenciesMatrixClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDenseMatrixClass_generated.DenseMatrixDependencies = {
	  MatrixDependencies: _dependenciesMatrixClassGenerated.MatrixDependencies,
	  createDenseMatrixClass: _factoriesAny.createDenseMatrixClass
	};
	return dependenciesDenseMatrixClass_generated;
}

var dependenciesFractionClass_generated = {};

var hasRequiredDependenciesFractionClass_generated;

function requireDependenciesFractionClass_generated () {
	if (hasRequiredDependenciesFractionClass_generated) return dependenciesFractionClass_generated;
	hasRequiredDependenciesFractionClass_generated = 1;

	Object.defineProperty(dependenciesFractionClass_generated, "__esModule", {
	  value: true
	});
	dependenciesFractionClass_generated.FractionDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFractionClass_generated.FractionDependencies = {
	  createFractionClass: _factoriesAny.createFractionClass
	};
	return dependenciesFractionClass_generated;
}

var hasRequiredDependenciesTyped_generated;

function requireDependenciesTyped_generated () {
	if (hasRequiredDependenciesTyped_generated) return dependenciesTyped_generated;
	hasRequiredDependenciesTyped_generated = 1;

	Object.defineProperty(dependenciesTyped_generated, "__esModule", {
	  value: true
	});
	dependenciesTyped_generated.typedDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTyped_generated.typedDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  createTyped: _factoriesAny.createTyped
	};
	return dependenciesTyped_generated;
}

var hasRequiredDependenciesAbs_generated;

function requireDependenciesAbs_generated () {
	if (hasRequiredDependenciesAbs_generated) return dependenciesAbs_generated;
	hasRequiredDependenciesAbs_generated = 1;

	Object.defineProperty(dependenciesAbs_generated, "__esModule", {
	  value: true
	});
	dependenciesAbs_generated.absDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAbs_generated.absDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAbs: _factoriesAny.createAbs
	};
	return dependenciesAbs_generated;
}

var dependenciesAccessorNode_generated = {};

var dependenciesNode_generated = {};

var hasRequiredDependenciesNode_generated;

function requireDependenciesNode_generated () {
	if (hasRequiredDependenciesNode_generated) return dependenciesNode_generated;
	hasRequiredDependenciesNode_generated = 1;

	Object.defineProperty(dependenciesNode_generated, "__esModule", {
	  value: true
	});
	dependenciesNode_generated.NodeDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNode_generated.NodeDependencies = {
	  createNode: _factoriesAny.createNode
	};
	return dependenciesNode_generated;
}

var dependenciesSubset_generated = {};

var dependenciesAdd_generated = {};

var dependenciesSparseMatrixClass_generated = {};

var dependenciesEqualScalar_generated = {};

var hasRequiredDependenciesEqualScalar_generated;

function requireDependenciesEqualScalar_generated () {
	if (hasRequiredDependenciesEqualScalar_generated) return dependenciesEqualScalar_generated;
	hasRequiredDependenciesEqualScalar_generated = 1;

	Object.defineProperty(dependenciesEqualScalar_generated, "__esModule", {
	  value: true
	});
	dependenciesEqualScalar_generated.equalScalarDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesEqualScalar_generated.equalScalarDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createEqualScalar: _factoriesAny.createEqualScalar
	};
	return dependenciesEqualScalar_generated;
}

var hasRequiredDependenciesSparseMatrixClass_generated;

function requireDependenciesSparseMatrixClass_generated () {
	if (hasRequiredDependenciesSparseMatrixClass_generated) return dependenciesSparseMatrixClass_generated;
	hasRequiredDependenciesSparseMatrixClass_generated = 1;

	Object.defineProperty(dependenciesSparseMatrixClass_generated, "__esModule", {
	  value: true
	});
	dependenciesSparseMatrixClass_generated.SparseMatrixDependencies = void 0;
	var _dependenciesMatrixClassGenerated = requireDependenciesMatrixClass_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSparseMatrixClass_generated.SparseMatrixDependencies = {
	  MatrixDependencies: _dependenciesMatrixClassGenerated.MatrixDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSparseMatrixClass: _factoriesAny.createSparseMatrixClass
	};
	return dependenciesSparseMatrixClass_generated;
}

var dependenciesAddScalar_generated = {};

var hasRequiredDependenciesAddScalar_generated;

function requireDependenciesAddScalar_generated () {
	if (hasRequiredDependenciesAddScalar_generated) return dependenciesAddScalar_generated;
	hasRequiredDependenciesAddScalar_generated = 1;

	Object.defineProperty(dependenciesAddScalar_generated, "__esModule", {
	  value: true
	});
	dependenciesAddScalar_generated.addScalarDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAddScalar_generated.addScalarDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAddScalar: _factoriesAny.createAddScalar
	};
	return dependenciesAddScalar_generated;
}

var dependenciesConcat_generated = {};

var dependenciesIsInteger_generated = {};

var dependenciesEqual_generated = {};

var dependenciesMatrix_generated = {};

var hasRequiredDependenciesMatrix_generated;

function requireDependenciesMatrix_generated () {
	if (hasRequiredDependenciesMatrix_generated) return dependenciesMatrix_generated;
	hasRequiredDependenciesMatrix_generated = 1;

	Object.defineProperty(dependenciesMatrix_generated, "__esModule", {
	  value: true
	});
	dependenciesMatrix_generated.matrixDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesMatrixClassGenerated = requireDependenciesMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMatrix_generated.matrixDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  MatrixDependencies: _dependenciesMatrixClassGenerated.MatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMatrix: _factoriesAny.createMatrix
	};
	return dependenciesMatrix_generated;
}

var hasRequiredDependenciesEqual_generated;

function requireDependenciesEqual_generated () {
	if (hasRequiredDependenciesEqual_generated) return dependenciesEqual_generated;
	hasRequiredDependenciesEqual_generated = 1;

	Object.defineProperty(dependenciesEqual_generated, "__esModule", {
	  value: true
	});
	dependenciesEqual_generated.equalDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesEqual_generated.equalDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createEqual: _factoriesAny.createEqual
	};
	return dependenciesEqual_generated;
}

var hasRequiredDependenciesIsInteger_generated;

function requireDependenciesIsInteger_generated () {
	if (hasRequiredDependenciesIsInteger_generated) return dependenciesIsInteger_generated;
	hasRequiredDependenciesIsInteger_generated = 1;

	Object.defineProperty(dependenciesIsInteger_generated, "__esModule", {
	  value: true
	});
	dependenciesIsInteger_generated.isIntegerDependencies = void 0;
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsInteger_generated.isIntegerDependencies = {
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsInteger: _factoriesAny.createIsInteger
	};
	return dependenciesIsInteger_generated;
}

var hasRequiredDependenciesConcat_generated;

function requireDependenciesConcat_generated () {
	if (hasRequiredDependenciesConcat_generated) return dependenciesConcat_generated;
	hasRequiredDependenciesConcat_generated = 1;

	Object.defineProperty(dependenciesConcat_generated, "__esModule", {
	  value: true
	});
	dependenciesConcat_generated.concatDependencies = void 0;
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesConcat_generated.concatDependencies = {
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createConcat: _factoriesAny.createConcat
	};
	return dependenciesConcat_generated;
}

var hasRequiredDependenciesAdd_generated;

function requireDependenciesAdd_generated () {
	if (hasRequiredDependenciesAdd_generated) return dependenciesAdd_generated;
	hasRequiredDependenciesAdd_generated = 1;

	Object.defineProperty(dependenciesAdd_generated, "__esModule", {
	  value: true
	});
	dependenciesAdd_generated.addDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAdd_generated.addDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAdd: _factoriesAny.createAdd
	};
	return dependenciesAdd_generated;
}

var dependenciesZeros_generated = {};

var hasRequiredDependenciesZeros_generated;

function requireDependenciesZeros_generated () {
	if (hasRequiredDependenciesZeros_generated) return dependenciesZeros_generated;
	hasRequiredDependenciesZeros_generated = 1;

	Object.defineProperty(dependenciesZeros_generated, "__esModule", {
	  value: true
	});
	dependenciesZeros_generated.zerosDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesZeros_generated.zerosDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createZeros: _factoriesAny.createZeros
	};
	return dependenciesZeros_generated;
}

var hasRequiredDependenciesSubset_generated;

function requireDependenciesSubset_generated () {
	if (hasRequiredDependenciesSubset_generated) return dependenciesSubset_generated;
	hasRequiredDependenciesSubset_generated = 1;

	Object.defineProperty(dependenciesSubset_generated, "__esModule", {
	  value: true
	});
	dependenciesSubset_generated.subsetDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSubset_generated.subsetDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createSubset: _factoriesAny.createSubset
	};
	return dependenciesSubset_generated;
}

var hasRequiredDependenciesAccessorNode_generated;

function requireDependenciesAccessorNode_generated () {
	if (hasRequiredDependenciesAccessorNode_generated) return dependenciesAccessorNode_generated;
	hasRequiredDependenciesAccessorNode_generated = 1;

	Object.defineProperty(dependenciesAccessorNode_generated, "__esModule", {
	  value: true
	});
	dependenciesAccessorNode_generated.AccessorNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAccessorNode_generated.AccessorNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  createAccessorNode: _factoriesAny.createAccessorNode
	};
	return dependenciesAccessorNode_generated;
}

var dependenciesAcos_generated = {};

var hasRequiredDependenciesAcos_generated;

function requireDependenciesAcos_generated () {
	if (hasRequiredDependenciesAcos_generated) return dependenciesAcos_generated;
	hasRequiredDependenciesAcos_generated = 1;

	Object.defineProperty(dependenciesAcos_generated, "__esModule", {
	  value: true
	});
	dependenciesAcos_generated.acosDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAcos_generated.acosDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAcos: _factoriesAny.createAcos
	};
	return dependenciesAcos_generated;
}

var dependenciesAcosh_generated = {};

var hasRequiredDependenciesAcosh_generated;

function requireDependenciesAcosh_generated () {
	if (hasRequiredDependenciesAcosh_generated) return dependenciesAcosh_generated;
	hasRequiredDependenciesAcosh_generated = 1;

	Object.defineProperty(dependenciesAcosh_generated, "__esModule", {
	  value: true
	});
	dependenciesAcosh_generated.acoshDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAcosh_generated.acoshDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAcosh: _factoriesAny.createAcosh
	};
	return dependenciesAcosh_generated;
}

var dependenciesAcot_generated = {};

var hasRequiredDependenciesAcot_generated;

function requireDependenciesAcot_generated () {
	if (hasRequiredDependenciesAcot_generated) return dependenciesAcot_generated;
	hasRequiredDependenciesAcot_generated = 1;

	Object.defineProperty(dependenciesAcot_generated, "__esModule", {
	  value: true
	});
	dependenciesAcot_generated.acotDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAcot_generated.acotDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAcot: _factoriesAny.createAcot
	};
	return dependenciesAcot_generated;
}

var dependenciesAcoth_generated = {};

var hasRequiredDependenciesAcoth_generated;

function requireDependenciesAcoth_generated () {
	if (hasRequiredDependenciesAcoth_generated) return dependenciesAcoth_generated;
	hasRequiredDependenciesAcoth_generated = 1;

	Object.defineProperty(dependenciesAcoth_generated, "__esModule", {
	  value: true
	});
	dependenciesAcoth_generated.acothDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAcoth_generated.acothDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAcoth: _factoriesAny.createAcoth
	};
	return dependenciesAcoth_generated;
}

var dependenciesAcsc_generated = {};

var hasRequiredDependenciesAcsc_generated;

function requireDependenciesAcsc_generated () {
	if (hasRequiredDependenciesAcsc_generated) return dependenciesAcsc_generated;
	hasRequiredDependenciesAcsc_generated = 1;

	Object.defineProperty(dependenciesAcsc_generated, "__esModule", {
	  value: true
	});
	dependenciesAcsc_generated.acscDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAcsc_generated.acscDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAcsc: _factoriesAny.createAcsc
	};
	return dependenciesAcsc_generated;
}

var dependenciesAcsch_generated = {};

var hasRequiredDependenciesAcsch_generated;

function requireDependenciesAcsch_generated () {
	if (hasRequiredDependenciesAcsch_generated) return dependenciesAcsch_generated;
	hasRequiredDependenciesAcsch_generated = 1;

	Object.defineProperty(dependenciesAcsch_generated, "__esModule", {
	  value: true
	});
	dependenciesAcsch_generated.acschDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAcsch_generated.acschDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAcsch: _factoriesAny.createAcsch
	};
	return dependenciesAcsch_generated;
}

var dependenciesAnd_generated = {};

var dependenciesNot_generated = {};

var hasRequiredDependenciesNot_generated;

function requireDependenciesNot_generated () {
	if (hasRequiredDependenciesNot_generated) return dependenciesNot_generated;
	hasRequiredDependenciesNot_generated = 1;

	Object.defineProperty(dependenciesNot_generated, "__esModule", {
	  value: true
	});
	dependenciesNot_generated.notDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNot_generated.notDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNot: _factoriesAny.createNot
	};
	return dependenciesNot_generated;
}

var hasRequiredDependenciesAnd_generated;

function requireDependenciesAnd_generated () {
	if (hasRequiredDependenciesAnd_generated) return dependenciesAnd_generated;
	hasRequiredDependenciesAnd_generated = 1;

	Object.defineProperty(dependenciesAnd_generated, "__esModule", {
	  value: true
	});
	dependenciesAnd_generated.andDependencies = void 0;
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesNotGenerated = requireDependenciesNot_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAnd_generated.andDependencies = {
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  notDependencies: _dependenciesNotGenerated.notDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createAnd: _factoriesAny.createAnd
	};
	return dependenciesAnd_generated;
}

var dependenciesAndTransform_generated = {};

var hasRequiredDependenciesAndTransform_generated;

function requireDependenciesAndTransform_generated () {
	if (hasRequiredDependenciesAndTransform_generated) return dependenciesAndTransform_generated;
	hasRequiredDependenciesAndTransform_generated = 1;

	Object.defineProperty(dependenciesAndTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesAndTransform_generated.andTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesNotGenerated = requireDependenciesNot_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAndTransform_generated.andTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  notDependencies: _dependenciesNotGenerated.notDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createAndTransform: _factoriesAny.createAndTransform
	};
	return dependenciesAndTransform_generated;
}

var dependenciesArg_generated = {};

var hasRequiredDependenciesArg_generated;

function requireDependenciesArg_generated () {
	if (hasRequiredDependenciesArg_generated) return dependenciesArg_generated;
	hasRequiredDependenciesArg_generated = 1;

	Object.defineProperty(dependenciesArg_generated, "__esModule", {
	  value: true
	});
	dependenciesArg_generated.argDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesArg_generated.argDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createArg: _factoriesAny.createArg
	};
	return dependenciesArg_generated;
}

var dependenciesArrayNode_generated = {};

var hasRequiredDependenciesArrayNode_generated;

function requireDependenciesArrayNode_generated () {
	if (hasRequiredDependenciesArrayNode_generated) return dependenciesArrayNode_generated;
	hasRequiredDependenciesArrayNode_generated = 1;

	Object.defineProperty(dependenciesArrayNode_generated, "__esModule", {
	  value: true
	});
	dependenciesArrayNode_generated.ArrayNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesArrayNode_generated.ArrayNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createArrayNode: _factoriesAny.createArrayNode
	};
	return dependenciesArrayNode_generated;
}

var dependenciesAsec_generated = {};

var hasRequiredDependenciesAsec_generated;

function requireDependenciesAsec_generated () {
	if (hasRequiredDependenciesAsec_generated) return dependenciesAsec_generated;
	hasRequiredDependenciesAsec_generated = 1;

	Object.defineProperty(dependenciesAsec_generated, "__esModule", {
	  value: true
	});
	dependenciesAsec_generated.asecDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAsec_generated.asecDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAsec: _factoriesAny.createAsec
	};
	return dependenciesAsec_generated;
}

var dependenciesAsech_generated = {};

var hasRequiredDependenciesAsech_generated;

function requireDependenciesAsech_generated () {
	if (hasRequiredDependenciesAsech_generated) return dependenciesAsech_generated;
	hasRequiredDependenciesAsech_generated = 1;

	Object.defineProperty(dependenciesAsech_generated, "__esModule", {
	  value: true
	});
	dependenciesAsech_generated.asechDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAsech_generated.asechDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAsech: _factoriesAny.createAsech
	};
	return dependenciesAsech_generated;
}

var dependenciesAsin_generated = {};

var hasRequiredDependenciesAsin_generated;

function requireDependenciesAsin_generated () {
	if (hasRequiredDependenciesAsin_generated) return dependenciesAsin_generated;
	hasRequiredDependenciesAsin_generated = 1;

	Object.defineProperty(dependenciesAsin_generated, "__esModule", {
	  value: true
	});
	dependenciesAsin_generated.asinDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAsin_generated.asinDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAsin: _factoriesAny.createAsin
	};
	return dependenciesAsin_generated;
}

var dependenciesAsinh_generated = {};

var hasRequiredDependenciesAsinh_generated;

function requireDependenciesAsinh_generated () {
	if (hasRequiredDependenciesAsinh_generated) return dependenciesAsinh_generated;
	hasRequiredDependenciesAsinh_generated = 1;

	Object.defineProperty(dependenciesAsinh_generated, "__esModule", {
	  value: true
	});
	dependenciesAsinh_generated.asinhDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAsinh_generated.asinhDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAsinh: _factoriesAny.createAsinh
	};
	return dependenciesAsinh_generated;
}

var dependenciesAssignmentNode_generated = {};

var hasRequiredDependenciesAssignmentNode_generated;

function requireDependenciesAssignmentNode_generated () {
	if (hasRequiredDependenciesAssignmentNode_generated) return dependenciesAssignmentNode_generated;
	hasRequiredDependenciesAssignmentNode_generated = 1;

	Object.defineProperty(dependenciesAssignmentNode_generated, "__esModule", {
	  value: true
	});
	dependenciesAssignmentNode_generated.AssignmentNodeDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAssignmentNode_generated.AssignmentNodeDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  createAssignmentNode: _factoriesAny.createAssignmentNode
	};
	return dependenciesAssignmentNode_generated;
}

var dependenciesAtan_generated = {};

var hasRequiredDependenciesAtan_generated;

function requireDependenciesAtan_generated () {
	if (hasRequiredDependenciesAtan_generated) return dependenciesAtan_generated;
	hasRequiredDependenciesAtan_generated = 1;

	Object.defineProperty(dependenciesAtan_generated, "__esModule", {
	  value: true
	});
	dependenciesAtan_generated.atanDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAtan_generated.atanDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAtan: _factoriesAny.createAtan
	};
	return dependenciesAtan_generated;
}

var dependenciesAtan2_generated = {};

var hasRequiredDependenciesAtan2_generated;

function requireDependenciesAtan2_generated () {
	if (hasRequiredDependenciesAtan2_generated) return dependenciesAtan2_generated;
	hasRequiredDependenciesAtan2_generated = 1;

	Object.defineProperty(dependenciesAtan2_generated, "__esModule", {
	  value: true
	});
	dependenciesAtan2_generated.atan2Dependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAtan2_generated.atan2Dependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAtan2: _factoriesAny.createAtan2
	};
	return dependenciesAtan2_generated;
}

var dependenciesAtanh_generated = {};

var hasRequiredDependenciesAtanh_generated;

function requireDependenciesAtanh_generated () {
	if (hasRequiredDependenciesAtanh_generated) return dependenciesAtanh_generated;
	hasRequiredDependenciesAtanh_generated = 1;

	Object.defineProperty(dependenciesAtanh_generated, "__esModule", {
	  value: true
	});
	dependenciesAtanh_generated.atanhDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAtanh_generated.atanhDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createAtanh: _factoriesAny.createAtanh
	};
	return dependenciesAtanh_generated;
}

var dependenciesAtomicMass_generated = {};

var dependenciesUnitClass_generated = {};

var dependenciesDivideScalar_generated = {};

var dependenciesNumeric_generated = {};

var dependenciesBignumber_generated = {};

var hasRequiredDependenciesBignumber_generated;

function requireDependenciesBignumber_generated () {
	if (hasRequiredDependenciesBignumber_generated) return dependenciesBignumber_generated;
	hasRequiredDependenciesBignumber_generated = 1;

	Object.defineProperty(dependenciesBignumber_generated, "__esModule", {
	  value: true
	});
	dependenciesBignumber_generated.bignumberDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBignumber_generated.bignumberDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBignumber: _factoriesAny.createBignumber
	};
	return dependenciesBignumber_generated;
}

var dependenciesFraction_generated = {};

var hasRequiredDependenciesFraction_generated;

function requireDependenciesFraction_generated () {
	if (hasRequiredDependenciesFraction_generated) return dependenciesFraction_generated;
	hasRequiredDependenciesFraction_generated = 1;

	Object.defineProperty(dependenciesFraction_generated, "__esModule", {
	  value: true
	});
	dependenciesFraction_generated.fractionDependencies = void 0;
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFraction_generated.fractionDependencies = {
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFraction: _factoriesAny.createFraction
	};
	return dependenciesFraction_generated;
}

var dependenciesNumber_generated = {};

var hasRequiredDependenciesNumber_generated;

function requireDependenciesNumber_generated () {
	if (hasRequiredDependenciesNumber_generated) return dependenciesNumber_generated;
	hasRequiredDependenciesNumber_generated = 1;

	Object.defineProperty(dependenciesNumber_generated, "__esModule", {
	  value: true
	});
	dependenciesNumber_generated.numberDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNumber_generated.numberDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNumber: _factoriesAny.createNumber
	};
	return dependenciesNumber_generated;
}

var hasRequiredDependenciesNumeric_generated;

function requireDependenciesNumeric_generated () {
	if (hasRequiredDependenciesNumeric_generated) return dependenciesNumeric_generated;
	hasRequiredDependenciesNumeric_generated = 1;

	Object.defineProperty(dependenciesNumeric_generated, "__esModule", {
	  value: true
	});
	dependenciesNumeric_generated.numericDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNumeric_generated.numericDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  fractionDependencies: _dependenciesFractionGenerated.fractionDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  createNumeric: _factoriesAny.createNumeric
	};
	return dependenciesNumeric_generated;
}

var hasRequiredDependenciesDivideScalar_generated;

function requireDependenciesDivideScalar_generated () {
	if (hasRequiredDependenciesDivideScalar_generated) return dependenciesDivideScalar_generated;
	hasRequiredDependenciesDivideScalar_generated = 1;

	Object.defineProperty(dependenciesDivideScalar_generated, "__esModule", {
	  value: true
	});
	dependenciesDivideScalar_generated.divideScalarDependencies = void 0;
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDivideScalar_generated.divideScalarDependencies = {
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDivideScalar: _factoriesAny.createDivideScalar
	};
	return dependenciesDivideScalar_generated;
}

var dependenciesFix_generated = {};

var dependenciesCeil_generated = {};

var dependenciesRound_generated = {};

var hasRequiredDependenciesRound_generated;

function requireDependenciesRound_generated () {
	if (hasRequiredDependenciesRound_generated) return dependenciesRound_generated;
	hasRequiredDependenciesRound_generated = 1;

	Object.defineProperty(dependenciesRound_generated, "__esModule", {
	  value: true
	});
	dependenciesRound_generated.roundDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRound_generated.roundDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createRound: _factoriesAny.createRound
	};
	return dependenciesRound_generated;
}

var hasRequiredDependenciesCeil_generated;

function requireDependenciesCeil_generated () {
	if (hasRequiredDependenciesCeil_generated) return dependenciesCeil_generated;
	hasRequiredDependenciesCeil_generated = 1;

	Object.defineProperty(dependenciesCeil_generated, "__esModule", {
	  value: true
	});
	dependenciesCeil_generated.ceilDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRoundGenerated = requireDependenciesRound_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCeil_generated.ceilDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  roundDependencies: _dependenciesRoundGenerated.roundDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createCeil: _factoriesAny.createCeil
	};
	return dependenciesCeil_generated;
}

var dependenciesFloor_generated = {};

var hasRequiredDependenciesFloor_generated;

function requireDependenciesFloor_generated () {
	if (hasRequiredDependenciesFloor_generated) return dependenciesFloor_generated;
	hasRequiredDependenciesFloor_generated = 1;

	Object.defineProperty(dependenciesFloor_generated, "__esModule", {
	  value: true
	});
	dependenciesFloor_generated.floorDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRoundGenerated = requireDependenciesRound_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFloor_generated.floorDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  roundDependencies: _dependenciesRoundGenerated.roundDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createFloor: _factoriesAny.createFloor
	};
	return dependenciesFloor_generated;
}

var hasRequiredDependenciesFix_generated;

function requireDependenciesFix_generated () {
	if (hasRequiredDependenciesFix_generated) return dependenciesFix_generated;
	hasRequiredDependenciesFix_generated = 1;

	Object.defineProperty(dependenciesFix_generated, "__esModule", {
	  value: true
	});
	dependenciesFix_generated.fixDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesCeilGenerated = requireDependenciesCeil_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesFloorGenerated = requireDependenciesFloor_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFix_generated.fixDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  ceilDependencies: _dependenciesCeilGenerated.ceilDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  floorDependencies: _dependenciesFloorGenerated.floorDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createFix: _factoriesAny.createFix
	};
	return dependenciesFix_generated;
}

var dependenciesFormat_generated = {};

var hasRequiredDependenciesFormat_generated;

function requireDependenciesFormat_generated () {
	if (hasRequiredDependenciesFormat_generated) return dependenciesFormat_generated;
	hasRequiredDependenciesFormat_generated = 1;

	Object.defineProperty(dependenciesFormat_generated, "__esModule", {
	  value: true
	});
	dependenciesFormat_generated.formatDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFormat_generated.formatDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFormat: _factoriesAny.createFormat
	};
	return dependenciesFormat_generated;
}

var dependenciesIsNumeric_generated = {};

var hasRequiredDependenciesIsNumeric_generated;

function requireDependenciesIsNumeric_generated () {
	if (hasRequiredDependenciesIsNumeric_generated) return dependenciesIsNumeric_generated;
	hasRequiredDependenciesIsNumeric_generated = 1;

	Object.defineProperty(dependenciesIsNumeric_generated, "__esModule", {
	  value: true
	});
	dependenciesIsNumeric_generated.isNumericDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsNumeric_generated.isNumericDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsNumeric: _factoriesAny.createIsNumeric
	};
	return dependenciesIsNumeric_generated;
}

var dependenciesMultiplyScalar_generated = {};

var hasRequiredDependenciesMultiplyScalar_generated;

function requireDependenciesMultiplyScalar_generated () {
	if (hasRequiredDependenciesMultiplyScalar_generated) return dependenciesMultiplyScalar_generated;
	hasRequiredDependenciesMultiplyScalar_generated = 1;

	Object.defineProperty(dependenciesMultiplyScalar_generated, "__esModule", {
	  value: true
	});
	dependenciesMultiplyScalar_generated.multiplyScalarDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMultiplyScalar_generated.multiplyScalarDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMultiplyScalar: _factoriesAny.createMultiplyScalar
	};
	return dependenciesMultiplyScalar_generated;
}

var dependenciesPow_generated = {};

var dependenciesIdentity_generated = {};

var hasRequiredDependenciesIdentity_generated;

function requireDependenciesIdentity_generated () {
	if (hasRequiredDependenciesIdentity_generated) return dependenciesIdentity_generated;
	hasRequiredDependenciesIdentity_generated = 1;

	Object.defineProperty(dependenciesIdentity_generated, "__esModule", {
	  value: true
	});
	dependenciesIdentity_generated.identityDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIdentity_generated.identityDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIdentity: _factoriesAny.createIdentity
	};
	return dependenciesIdentity_generated;
}

var dependenciesInv_generated = {};

var dependenciesDet_generated = {};

var dependenciesIsZero_generated = {};

var hasRequiredDependenciesIsZero_generated;

function requireDependenciesIsZero_generated () {
	if (hasRequiredDependenciesIsZero_generated) return dependenciesIsZero_generated;
	hasRequiredDependenciesIsZero_generated = 1;

	Object.defineProperty(dependenciesIsZero_generated, "__esModule", {
	  value: true
	});
	dependenciesIsZero_generated.isZeroDependencies = void 0;
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsZero_generated.isZeroDependencies = {
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsZero: _factoriesAny.createIsZero
	};
	return dependenciesIsZero_generated;
}

var dependenciesMultiply_generated = {};

var dependenciesDot_generated = {};

var dependenciesConj_generated = {};

var hasRequiredDependenciesConj_generated;

function requireDependenciesConj_generated () {
	if (hasRequiredDependenciesConj_generated) return dependenciesConj_generated;
	hasRequiredDependenciesConj_generated = 1;

	Object.defineProperty(dependenciesConj_generated, "__esModule", {
	  value: true
	});
	dependenciesConj_generated.conjDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesConj_generated.conjDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createConj: _factoriesAny.createConj
	};
	return dependenciesConj_generated;
}

var dependenciesSize_generated = {};

var hasRequiredDependenciesSize_generated;

function requireDependenciesSize_generated () {
	if (hasRequiredDependenciesSize_generated) return dependenciesSize_generated;
	hasRequiredDependenciesSize_generated = 1;

	Object.defineProperty(dependenciesSize_generated, "__esModule", {
	  value: true
	});
	dependenciesSize_generated.sizeDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSize_generated.sizeDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSize: _factoriesAny.createSize
	};
	return dependenciesSize_generated;
}

var hasRequiredDependenciesDot_generated;

function requireDependenciesDot_generated () {
	if (hasRequiredDependenciesDot_generated) return dependenciesDot_generated;
	hasRequiredDependenciesDot_generated = 1;

	Object.defineProperty(dependenciesDot_generated, "__esModule", {
	  value: true
	});
	dependenciesDot_generated.dotDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesConjGenerated = requireDependenciesConj_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDot_generated.dotDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  conjDependencies: _dependenciesConjGenerated.conjDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDot: _factoriesAny.createDot
	};
	return dependenciesDot_generated;
}

var hasRequiredDependenciesMultiply_generated;

function requireDependenciesMultiply_generated () {
	if (hasRequiredDependenciesMultiply_generated) return dependenciesMultiply_generated;
	hasRequiredDependenciesMultiply_generated = 1;

	Object.defineProperty(dependenciesMultiply_generated, "__esModule", {
	  value: true
	});
	dependenciesMultiply_generated.multiplyDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDotGenerated = requireDependenciesDot_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMultiply_generated.multiplyDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  dotDependencies: _dependenciesDotGenerated.dotDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMultiply: _factoriesAny.createMultiply
	};
	return dependenciesMultiply_generated;
}

var dependenciesSubtractScalar_generated = {};

var hasRequiredDependenciesSubtractScalar_generated;

function requireDependenciesSubtractScalar_generated () {
	if (hasRequiredDependenciesSubtractScalar_generated) return dependenciesSubtractScalar_generated;
	hasRequiredDependenciesSubtractScalar_generated = 1;

	Object.defineProperty(dependenciesSubtractScalar_generated, "__esModule", {
	  value: true
	});
	dependenciesSubtractScalar_generated.subtractScalarDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSubtractScalar_generated.subtractScalarDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSubtractScalar: _factoriesAny.createSubtractScalar
	};
	return dependenciesSubtractScalar_generated;
}

var dependenciesUnaryMinus_generated = {};

var hasRequiredDependenciesUnaryMinus_generated;

function requireDependenciesUnaryMinus_generated () {
	if (hasRequiredDependenciesUnaryMinus_generated) return dependenciesUnaryMinus_generated;
	hasRequiredDependenciesUnaryMinus_generated = 1;

	Object.defineProperty(dependenciesUnaryMinus_generated, "__esModule", {
	  value: true
	});
	dependenciesUnaryMinus_generated.unaryMinusDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUnaryMinus_generated.unaryMinusDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createUnaryMinus: _factoriesAny.createUnaryMinus
	};
	return dependenciesUnaryMinus_generated;
}

var hasRequiredDependenciesDet_generated;

function requireDependenciesDet_generated () {
	if (hasRequiredDependenciesDet_generated) return dependenciesDet_generated;
	hasRequiredDependenciesDet_generated = 1;

	Object.defineProperty(dependenciesDet_generated, "__esModule", {
	  value: true
	});
	dependenciesDet_generated.detDependencies = void 0;
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDet_generated.detDependencies = {
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createDet: _factoriesAny.createDet
	};
	return dependenciesDet_generated;
}

var hasRequiredDependenciesInv_generated;

function requireDependenciesInv_generated () {
	if (hasRequiredDependenciesInv_generated) return dependenciesInv_generated;
	hasRequiredDependenciesInv_generated = 1;

	Object.defineProperty(dependenciesInv_generated, "__esModule", {
	  value: true
	});
	dependenciesInv_generated.invDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDetGenerated = requireDependenciesDet_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesInv_generated.invDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  detDependencies: _dependenciesDetGenerated.detDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createInv: _factoriesAny.createInv
	};
	return dependenciesInv_generated;
}

var hasRequiredDependenciesPow_generated;

function requireDependenciesPow_generated () {
	if (hasRequiredDependenciesPow_generated) return dependenciesPow_generated;
	hasRequiredDependenciesPow_generated = 1;

	Object.defineProperty(dependenciesPow_generated, "__esModule", {
	  value: true
	});
	dependenciesPow_generated.powDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesInvGenerated = requireDependenciesInv_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPow_generated.powDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  fractionDependencies: _dependenciesFractionGenerated.fractionDependencies,
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  invDependencies: _dependenciesInvGenerated.invDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createPow: _factoriesAny.createPow
	};
	return dependenciesPow_generated;
}

var hasRequiredDependenciesUnitClass_generated;

function requireDependenciesUnitClass_generated () {
	if (hasRequiredDependenciesUnitClass_generated) return dependenciesUnitClass_generated;
	hasRequiredDependenciesUnitClass_generated = 1;

	Object.defineProperty(dependenciesUnitClass_generated, "__esModule", {
	  value: true
	});
	dependenciesUnitClass_generated.UnitDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesFixGenerated = requireDependenciesFix_generated();
	var _dependenciesFormatGenerated = requireDependenciesFormat_generated();
	var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesRoundGenerated = requireDependenciesRound_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUnitClass_generated.UnitDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  fixDependencies: _dependenciesFixGenerated.fixDependencies,
	  formatDependencies: _dependenciesFormatGenerated.formatDependencies,
	  isNumericDependencies: _dependenciesIsNumericGenerated.isNumericDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  roundDependencies: _dependenciesRoundGenerated.roundDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  createUnitClass: _factoriesAny.createUnitClass
	};
	return dependenciesUnitClass_generated;
}

var hasRequiredDependenciesAtomicMass_generated;

function requireDependenciesAtomicMass_generated () {
	if (hasRequiredDependenciesAtomicMass_generated) return dependenciesAtomicMass_generated;
	hasRequiredDependenciesAtomicMass_generated = 1;

	Object.defineProperty(dependenciesAtomicMass_generated, "__esModule", {
	  value: true
	});
	dependenciesAtomicMass_generated.atomicMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAtomicMass_generated.atomicMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createAtomicMass: _factoriesAny.createAtomicMass
	};
	return dependenciesAtomicMass_generated;
}

var dependenciesAvogadro_generated = {};

var hasRequiredDependenciesAvogadro_generated;

function requireDependenciesAvogadro_generated () {
	if (hasRequiredDependenciesAvogadro_generated) return dependenciesAvogadro_generated;
	hasRequiredDependenciesAvogadro_generated = 1;

	Object.defineProperty(dependenciesAvogadro_generated, "__esModule", {
	  value: true
	});
	dependenciesAvogadro_generated.avogadroDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesAvogadro_generated.avogadroDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createAvogadro: _factoriesAny.createAvogadro
	};
	return dependenciesAvogadro_generated;
}

var dependenciesBellNumbers_generated = {};

var dependenciesIsNegative_generated = {};

var hasRequiredDependenciesIsNegative_generated;

function requireDependenciesIsNegative_generated () {
	if (hasRequiredDependenciesIsNegative_generated) return dependenciesIsNegative_generated;
	hasRequiredDependenciesIsNegative_generated = 1;

	Object.defineProperty(dependenciesIsNegative_generated, "__esModule", {
	  value: true
	});
	dependenciesIsNegative_generated.isNegativeDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsNegative_generated.isNegativeDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsNegative: _factoriesAny.createIsNegative
	};
	return dependenciesIsNegative_generated;
}

var dependenciesStirlingS2_generated = {};

var dependenciesCombinations_generated = {};

var hasRequiredDependenciesCombinations_generated;

function requireDependenciesCombinations_generated () {
	if (hasRequiredDependenciesCombinations_generated) return dependenciesCombinations_generated;
	hasRequiredDependenciesCombinations_generated = 1;

	Object.defineProperty(dependenciesCombinations_generated, "__esModule", {
	  value: true
	});
	dependenciesCombinations_generated.combinationsDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCombinations_generated.combinationsDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCombinations: _factoriesAny.createCombinations
	};
	return dependenciesCombinations_generated;
}

var dependenciesFactorial_generated = {};

var dependenciesGamma_generated = {};

var hasRequiredDependenciesGamma_generated;

function requireDependenciesGamma_generated () {
	if (hasRequiredDependenciesGamma_generated) return dependenciesGamma_generated;
	hasRequiredDependenciesGamma_generated = 1;

	Object.defineProperty(dependenciesGamma_generated, "__esModule", {
	  value: true
	});
	dependenciesGamma_generated.gammaDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesGamma_generated.gammaDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createGamma: _factoriesAny.createGamma
	};
	return dependenciesGamma_generated;
}

var hasRequiredDependenciesFactorial_generated;

function requireDependenciesFactorial_generated () {
	if (hasRequiredDependenciesFactorial_generated) return dependenciesFactorial_generated;
	hasRequiredDependenciesFactorial_generated = 1;

	Object.defineProperty(dependenciesFactorial_generated, "__esModule", {
	  value: true
	});
	dependenciesFactorial_generated.factorialDependencies = void 0;
	var _dependenciesGammaGenerated = requireDependenciesGamma_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFactorial_generated.factorialDependencies = {
	  gammaDependencies: _dependenciesGammaGenerated.gammaDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFactorial: _factoriesAny.createFactorial
	};
	return dependenciesFactorial_generated;
}

var dependenciesLarger_generated = {};

var hasRequiredDependenciesLarger_generated;

function requireDependenciesLarger_generated () {
	if (hasRequiredDependenciesLarger_generated) return dependenciesLarger_generated;
	hasRequiredDependenciesLarger_generated = 1;

	Object.defineProperty(dependenciesLarger_generated, "__esModule", {
	  value: true
	});
	dependenciesLarger_generated.largerDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLarger_generated.largerDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLarger: _factoriesAny.createLarger
	};
	return dependenciesLarger_generated;
}

var hasRequiredDependenciesStirlingS2_generated;

function requireDependenciesStirlingS2_generated () {
	if (hasRequiredDependenciesStirlingS2_generated) return dependenciesStirlingS2_generated;
	hasRequiredDependenciesStirlingS2_generated = 1;

	Object.defineProperty(dependenciesStirlingS2_generated, "__esModule", {
	  value: true
	});
	dependenciesStirlingS2_generated.stirlingS2Dependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesCombinationsGenerated = requireDependenciesCombinations_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesFactorialGenerated = requireDependenciesFactorial_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesStirlingS2_generated.stirlingS2Dependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  combinationsDependencies: _dependenciesCombinationsGenerated.combinationsDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  factorialDependencies: _dependenciesFactorialGenerated.factorialDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createStirlingS2: _factoriesAny.createStirlingS2
	};
	return dependenciesStirlingS2_generated;
}

var hasRequiredDependenciesBellNumbers_generated;

function requireDependenciesBellNumbers_generated () {
	if (hasRequiredDependenciesBellNumbers_generated) return dependenciesBellNumbers_generated;
	hasRequiredDependenciesBellNumbers_generated = 1;

	Object.defineProperty(dependenciesBellNumbers_generated, "__esModule", {
	  value: true
	});
	dependenciesBellNumbers_generated.bellNumbersDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesStirlingS2Generated = requireDependenciesStirlingS2_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBellNumbers_generated.bellNumbersDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  stirlingS2Dependencies: _dependenciesStirlingS2Generated.stirlingS2Dependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBellNumbers: _factoriesAny.createBellNumbers
	};
	return dependenciesBellNumbers_generated;
}

var dependenciesBernoulli_generated = {};

var hasRequiredDependenciesBernoulli_generated;

function requireDependenciesBernoulli_generated () {
	if (hasRequiredDependenciesBernoulli_generated) return dependenciesBernoulli_generated;
	hasRequiredDependenciesBernoulli_generated = 1;

	Object.defineProperty(dependenciesBernoulli_generated, "__esModule", {
	  value: true
	});
	dependenciesBernoulli_generated.bernoulliDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBernoulli_generated.bernoulliDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBernoulli: _factoriesAny.createBernoulli
	};
	return dependenciesBernoulli_generated;
}

var dependenciesBigint_generated = {};

var hasRequiredDependenciesBigint_generated;

function requireDependenciesBigint_generated () {
	if (hasRequiredDependenciesBigint_generated) return dependenciesBigint_generated;
	hasRequiredDependenciesBigint_generated = 1;

	Object.defineProperty(dependenciesBigint_generated, "__esModule", {
	  value: true
	});
	dependenciesBigint_generated.bigintDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBigint_generated.bigintDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBigint: _factoriesAny.createBigint
	};
	return dependenciesBigint_generated;
}

var dependenciesBin_generated = {};

var hasRequiredDependenciesBin_generated;

function requireDependenciesBin_generated () {
	if (hasRequiredDependenciesBin_generated) return dependenciesBin_generated;
	hasRequiredDependenciesBin_generated = 1;

	Object.defineProperty(dependenciesBin_generated, "__esModule", {
	  value: true
	});
	dependenciesBin_generated.binDependencies = void 0;
	var _dependenciesFormatGenerated = requireDependenciesFormat_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBin_generated.binDependencies = {
	  formatDependencies: _dependenciesFormatGenerated.formatDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBin: _factoriesAny.createBin
	};
	return dependenciesBin_generated;
}

var dependenciesBitAnd_generated = {};

var hasRequiredDependenciesBitAnd_generated;

function requireDependenciesBitAnd_generated () {
	if (hasRequiredDependenciesBitAnd_generated) return dependenciesBitAnd_generated;
	hasRequiredDependenciesBitAnd_generated = 1;

	Object.defineProperty(dependenciesBitAnd_generated, "__esModule", {
	  value: true
	});
	dependenciesBitAnd_generated.bitAndDependencies = void 0;
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBitAnd_generated.bitAndDependencies = {
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBitAnd: _factoriesAny.createBitAnd
	};
	return dependenciesBitAnd_generated;
}

var dependenciesBitAndTransform_generated = {};

var hasRequiredDependenciesBitAndTransform_generated;

function requireDependenciesBitAndTransform_generated () {
	if (hasRequiredDependenciesBitAndTransform_generated) return dependenciesBitAndTransform_generated;
	hasRequiredDependenciesBitAndTransform_generated = 1;

	Object.defineProperty(dependenciesBitAndTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesBitAndTransform_generated.bitAndTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesNotGenerated = requireDependenciesNot_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBitAndTransform_generated.bitAndTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  notDependencies: _dependenciesNotGenerated.notDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createBitAndTransform: _factoriesAny.createBitAndTransform
	};
	return dependenciesBitAndTransform_generated;
}

var dependenciesBitNot_generated = {};

var hasRequiredDependenciesBitNot_generated;

function requireDependenciesBitNot_generated () {
	if (hasRequiredDependenciesBitNot_generated) return dependenciesBitNot_generated;
	hasRequiredDependenciesBitNot_generated = 1;

	Object.defineProperty(dependenciesBitNot_generated, "__esModule", {
	  value: true
	});
	dependenciesBitNot_generated.bitNotDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBitNot_generated.bitNotDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBitNot: _factoriesAny.createBitNot
	};
	return dependenciesBitNot_generated;
}

var dependenciesBitOr_generated = {};

var hasRequiredDependenciesBitOr_generated;

function requireDependenciesBitOr_generated () {
	if (hasRequiredDependenciesBitOr_generated) return dependenciesBitOr_generated;
	hasRequiredDependenciesBitOr_generated = 1;

	Object.defineProperty(dependenciesBitOr_generated, "__esModule", {
	  value: true
	});
	dependenciesBitOr_generated.bitOrDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBitOr_generated.bitOrDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBitOr: _factoriesAny.createBitOr
	};
	return dependenciesBitOr_generated;
}

var dependenciesBitOrTransform_generated = {};

var hasRequiredDependenciesBitOrTransform_generated;

function requireDependenciesBitOrTransform_generated () {
	if (hasRequiredDependenciesBitOrTransform_generated) return dependenciesBitOrTransform_generated;
	hasRequiredDependenciesBitOrTransform_generated = 1;

	Object.defineProperty(dependenciesBitOrTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesBitOrTransform_generated.bitOrTransformDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBitOrTransform_generated.bitOrTransformDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBitOrTransform: _factoriesAny.createBitOrTransform
	};
	return dependenciesBitOrTransform_generated;
}

var dependenciesBitXor_generated = {};

var hasRequiredDependenciesBitXor_generated;

function requireDependenciesBitXor_generated () {
	if (hasRequiredDependenciesBitXor_generated) return dependenciesBitXor_generated;
	hasRequiredDependenciesBitXor_generated = 1;

	Object.defineProperty(dependenciesBitXor_generated, "__esModule", {
	  value: true
	});
	dependenciesBitXor_generated.bitXorDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBitXor_generated.bitXorDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBitXor: _factoriesAny.createBitXor
	};
	return dependenciesBitXor_generated;
}

var dependenciesBlockNode_generated = {};

var dependenciesResultSet_generated = {};

var hasRequiredDependenciesResultSet_generated;

function requireDependenciesResultSet_generated () {
	if (hasRequiredDependenciesResultSet_generated) return dependenciesResultSet_generated;
	hasRequiredDependenciesResultSet_generated = 1;

	Object.defineProperty(dependenciesResultSet_generated, "__esModule", {
	  value: true
	});
	dependenciesResultSet_generated.ResultSetDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesResultSet_generated.ResultSetDependencies = {
	  createResultSet: _factoriesAny.createResultSet
	};
	return dependenciesResultSet_generated;
}

var hasRequiredDependenciesBlockNode_generated;

function requireDependenciesBlockNode_generated () {
	if (hasRequiredDependenciesBlockNode_generated) return dependenciesBlockNode_generated;
	hasRequiredDependenciesBlockNode_generated = 1;

	Object.defineProperty(dependenciesBlockNode_generated, "__esModule", {
	  value: true
	});
	dependenciesBlockNode_generated.BlockNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesResultSetGenerated = requireDependenciesResultSet_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBlockNode_generated.BlockNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  ResultSetDependencies: _dependenciesResultSetGenerated.ResultSetDependencies,
	  createBlockNode: _factoriesAny.createBlockNode
	};
	return dependenciesBlockNode_generated;
}

var dependenciesBohrMagneton_generated = {};

var hasRequiredDependenciesBohrMagneton_generated;

function requireDependenciesBohrMagneton_generated () {
	if (hasRequiredDependenciesBohrMagneton_generated) return dependenciesBohrMagneton_generated;
	hasRequiredDependenciesBohrMagneton_generated = 1;

	Object.defineProperty(dependenciesBohrMagneton_generated, "__esModule", {
	  value: true
	});
	dependenciesBohrMagneton_generated.bohrMagnetonDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBohrMagneton_generated.bohrMagnetonDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createBohrMagneton: _factoriesAny.createBohrMagneton
	};
	return dependenciesBohrMagneton_generated;
}

var dependenciesBohrRadius_generated = {};

var hasRequiredDependenciesBohrRadius_generated;

function requireDependenciesBohrRadius_generated () {
	if (hasRequiredDependenciesBohrRadius_generated) return dependenciesBohrRadius_generated;
	hasRequiredDependenciesBohrRadius_generated = 1;

	Object.defineProperty(dependenciesBohrRadius_generated, "__esModule", {
	  value: true
	});
	dependenciesBohrRadius_generated.bohrRadiusDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBohrRadius_generated.bohrRadiusDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createBohrRadius: _factoriesAny.createBohrRadius
	};
	return dependenciesBohrRadius_generated;
}

var dependenciesBoltzmann_generated = {};

var hasRequiredDependenciesBoltzmann_generated;

function requireDependenciesBoltzmann_generated () {
	if (hasRequiredDependenciesBoltzmann_generated) return dependenciesBoltzmann_generated;
	hasRequiredDependenciesBoltzmann_generated = 1;

	Object.defineProperty(dependenciesBoltzmann_generated, "__esModule", {
	  value: true
	});
	dependenciesBoltzmann_generated.boltzmannDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBoltzmann_generated.boltzmannDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createBoltzmann: _factoriesAny.createBoltzmann
	};
	return dependenciesBoltzmann_generated;
}

var dependenciesBoolean_generated = {};

var hasRequiredDependenciesBoolean_generated;

function requireDependenciesBoolean_generated () {
	if (hasRequiredDependenciesBoolean_generated) return dependenciesBoolean_generated;
	hasRequiredDependenciesBoolean_generated = 1;

	Object.defineProperty(dependenciesBoolean_generated, "__esModule", {
	  value: true
	});
	dependenciesBoolean_generated.booleanDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesBoolean_generated.booleanDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createBoolean: _factoriesAny.createBoolean
	};
	return dependenciesBoolean_generated;
}

var dependenciesCatalan_generated = {};

var hasRequiredDependenciesCatalan_generated;

function requireDependenciesCatalan_generated () {
	if (hasRequiredDependenciesCatalan_generated) return dependenciesCatalan_generated;
	hasRequiredDependenciesCatalan_generated = 1;

	Object.defineProperty(dependenciesCatalan_generated, "__esModule", {
	  value: true
	});
	dependenciesCatalan_generated.catalanDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesCombinationsGenerated = requireDependenciesCombinations_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCatalan_generated.catalanDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  combinationsDependencies: _dependenciesCombinationsGenerated.combinationsDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCatalan: _factoriesAny.createCatalan
	};
	return dependenciesCatalan_generated;
}

var dependenciesCbrt_generated = {};

var hasRequiredDependenciesCbrt_generated;

function requireDependenciesCbrt_generated () {
	if (hasRequiredDependenciesCbrt_generated) return dependenciesCbrt_generated;
	hasRequiredDependenciesCbrt_generated = 1;

	Object.defineProperty(dependenciesCbrt_generated, "__esModule", {
	  value: true
	});
	dependenciesCbrt_generated.cbrtDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCbrt_generated.cbrtDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createCbrt: _factoriesAny.createCbrt
	};
	return dependenciesCbrt_generated;
}

var dependenciesChain_generated = {};

var dependenciesChainClass_generated = {};

var hasRequiredDependenciesChainClass_generated;

function requireDependenciesChainClass_generated () {
	if (hasRequiredDependenciesChainClass_generated) return dependenciesChainClass_generated;
	hasRequiredDependenciesChainClass_generated = 1;

	Object.defineProperty(dependenciesChainClass_generated, "__esModule", {
	  value: true
	});
	dependenciesChainClass_generated.ChainDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesChainClass_generated.ChainDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createChainClass: _factoriesAny.createChainClass
	};
	return dependenciesChainClass_generated;
}

var hasRequiredDependenciesChain_generated;

function requireDependenciesChain_generated () {
	if (hasRequiredDependenciesChain_generated) return dependenciesChain_generated;
	hasRequiredDependenciesChain_generated = 1;

	Object.defineProperty(dependenciesChain_generated, "__esModule", {
	  value: true
	});
	dependenciesChain_generated.chainDependencies = void 0;
	var _dependenciesChainClassGenerated = requireDependenciesChainClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesChain_generated.chainDependencies = {
	  ChainDependencies: _dependenciesChainClassGenerated.ChainDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createChain: _factoriesAny.createChain
	};
	return dependenciesChain_generated;
}

var dependenciesClassicalElectronRadius_generated = {};

var hasRequiredDependenciesClassicalElectronRadius_generated;

function requireDependenciesClassicalElectronRadius_generated () {
	if (hasRequiredDependenciesClassicalElectronRadius_generated) return dependenciesClassicalElectronRadius_generated;
	hasRequiredDependenciesClassicalElectronRadius_generated = 1;

	Object.defineProperty(dependenciesClassicalElectronRadius_generated, "__esModule", {
	  value: true
	});
	dependenciesClassicalElectronRadius_generated.classicalElectronRadiusDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesClassicalElectronRadius_generated.classicalElectronRadiusDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createClassicalElectronRadius: _factoriesAny.createClassicalElectronRadius
	};
	return dependenciesClassicalElectronRadius_generated;
}

var dependenciesClone_generated = {};

var hasRequiredDependenciesClone_generated;

function requireDependenciesClone_generated () {
	if (hasRequiredDependenciesClone_generated) return dependenciesClone_generated;
	hasRequiredDependenciesClone_generated = 1;

	Object.defineProperty(dependenciesClone_generated, "__esModule", {
	  value: true
	});
	dependenciesClone_generated.cloneDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesClone_generated.cloneDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createClone: _factoriesAny.createClone
	};
	return dependenciesClone_generated;
}

var dependenciesColumn_generated = {};

var dependenciesIndexClass_generated = {};

var dependenciesImmutableDenseMatrixClass_generated = {};

var dependenciesSmaller_generated = {};

var hasRequiredDependenciesSmaller_generated;

function requireDependenciesSmaller_generated () {
	if (hasRequiredDependenciesSmaller_generated) return dependenciesSmaller_generated;
	hasRequiredDependenciesSmaller_generated = 1;

	Object.defineProperty(dependenciesSmaller_generated, "__esModule", {
	  value: true
	});
	dependenciesSmaller_generated.smallerDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSmaller_generated.smallerDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSmaller: _factoriesAny.createSmaller
	};
	return dependenciesSmaller_generated;
}

var hasRequiredDependenciesImmutableDenseMatrixClass_generated;

function requireDependenciesImmutableDenseMatrixClass_generated () {
	if (hasRequiredDependenciesImmutableDenseMatrixClass_generated) return dependenciesImmutableDenseMatrixClass_generated;
	hasRequiredDependenciesImmutableDenseMatrixClass_generated = 1;

	Object.defineProperty(dependenciesImmutableDenseMatrixClass_generated, "__esModule", {
	  value: true
	});
	dependenciesImmutableDenseMatrixClass_generated.ImmutableDenseMatrixDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesImmutableDenseMatrixClass_generated.ImmutableDenseMatrixDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  createImmutableDenseMatrixClass: _factoriesAny.createImmutableDenseMatrixClass
	};
	return dependenciesImmutableDenseMatrixClass_generated;
}

var dependenciesGetMatrixDataType_generated = {};

var hasRequiredDependenciesGetMatrixDataType_generated;

function requireDependenciesGetMatrixDataType_generated () {
	if (hasRequiredDependenciesGetMatrixDataType_generated) return dependenciesGetMatrixDataType_generated;
	hasRequiredDependenciesGetMatrixDataType_generated = 1;

	Object.defineProperty(dependenciesGetMatrixDataType_generated, "__esModule", {
	  value: true
	});
	dependenciesGetMatrixDataType_generated.getMatrixDataTypeDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesGetMatrixDataType_generated.getMatrixDataTypeDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createGetMatrixDataType: _factoriesAny.createGetMatrixDataType
	};
	return dependenciesGetMatrixDataType_generated;
}

var hasRequiredDependenciesIndexClass_generated;

function requireDependenciesIndexClass_generated () {
	if (hasRequiredDependenciesIndexClass_generated) return dependenciesIndexClass_generated;
	hasRequiredDependenciesIndexClass_generated = 1;

	Object.defineProperty(dependenciesIndexClass_generated, "__esModule", {
	  value: true
	});
	dependenciesIndexClass_generated.IndexDependencies = void 0;
	var _dependenciesImmutableDenseMatrixClassGenerated = requireDependenciesImmutableDenseMatrixClass_generated();
	var _dependenciesGetMatrixDataTypeGenerated = requireDependenciesGetMatrixDataType_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIndexClass_generated.IndexDependencies = {
	  ImmutableDenseMatrixDependencies: _dependenciesImmutableDenseMatrixClassGenerated.ImmutableDenseMatrixDependencies,
	  getMatrixDataTypeDependencies: _dependenciesGetMatrixDataTypeGenerated.getMatrixDataTypeDependencies,
	  createIndexClass: _factoriesAny.createIndexClass
	};
	return dependenciesIndexClass_generated;
}

var dependenciesRange_generated = {};

var dependenciesIsPositive_generated = {};

var hasRequiredDependenciesIsPositive_generated;

function requireDependenciesIsPositive_generated () {
	if (hasRequiredDependenciesIsPositive_generated) return dependenciesIsPositive_generated;
	hasRequiredDependenciesIsPositive_generated = 1;

	Object.defineProperty(dependenciesIsPositive_generated, "__esModule", {
	  value: true
	});
	dependenciesIsPositive_generated.isPositiveDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsPositive_generated.isPositiveDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsPositive: _factoriesAny.createIsPositive
	};
	return dependenciesIsPositive_generated;
}

var dependenciesLargerEq_generated = {};

var hasRequiredDependenciesLargerEq_generated;

function requireDependenciesLargerEq_generated () {
	if (hasRequiredDependenciesLargerEq_generated) return dependenciesLargerEq_generated;
	hasRequiredDependenciesLargerEq_generated = 1;

	Object.defineProperty(dependenciesLargerEq_generated, "__esModule", {
	  value: true
	});
	dependenciesLargerEq_generated.largerEqDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLargerEq_generated.largerEqDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLargerEq: _factoriesAny.createLargerEq
	};
	return dependenciesLargerEq_generated;
}

var dependenciesSmallerEq_generated = {};

var hasRequiredDependenciesSmallerEq_generated;

function requireDependenciesSmallerEq_generated () {
	if (hasRequiredDependenciesSmallerEq_generated) return dependenciesSmallerEq_generated;
	hasRequiredDependenciesSmallerEq_generated = 1;

	Object.defineProperty(dependenciesSmallerEq_generated, "__esModule", {
	  value: true
	});
	dependenciesSmallerEq_generated.smallerEqDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSmallerEq_generated.smallerEqDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSmallerEq: _factoriesAny.createSmallerEq
	};
	return dependenciesSmallerEq_generated;
}

var hasRequiredDependenciesRange_generated;

function requireDependenciesRange_generated () {
	if (hasRequiredDependenciesRange_generated) return dependenciesRange_generated;
	hasRequiredDependenciesRange_generated = 1;

	Object.defineProperty(dependenciesRange_generated, "__esModule", {
	  value: true
	});
	dependenciesRange_generated.rangeDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesLargerEqGenerated = requireDependenciesLargerEq_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSmallerEqGenerated = requireDependenciesSmallerEq_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRange_generated.rangeDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  isPositiveDependencies: _dependenciesIsPositiveGenerated.isPositiveDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  largerEqDependencies: _dependenciesLargerEqGenerated.largerEqDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  smallerEqDependencies: _dependenciesSmallerEqGenerated.smallerEqDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRange: _factoriesAny.createRange
	};
	return dependenciesRange_generated;
}

var hasRequiredDependenciesColumn_generated;

function requireDependenciesColumn_generated () {
	if (hasRequiredDependenciesColumn_generated) return dependenciesColumn_generated;
	hasRequiredDependenciesColumn_generated = 1;

	Object.defineProperty(dependenciesColumn_generated, "__esModule", {
	  value: true
	});
	dependenciesColumn_generated.columnDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRangeGenerated = requireDependenciesRange_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesColumn_generated.columnDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  rangeDependencies: _dependenciesRangeGenerated.rangeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createColumn: _factoriesAny.createColumn
	};
	return dependenciesColumn_generated;
}

var dependenciesColumnTransform_generated = {};

var hasRequiredDependenciesColumnTransform_generated;

function requireDependenciesColumnTransform_generated () {
	if (hasRequiredDependenciesColumnTransform_generated) return dependenciesColumnTransform_generated;
	hasRequiredDependenciesColumnTransform_generated = 1;

	Object.defineProperty(dependenciesColumnTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesColumnTransform_generated.columnTransformDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRangeGenerated = requireDependenciesRange_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesColumnTransform_generated.columnTransformDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  rangeDependencies: _dependenciesRangeGenerated.rangeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createColumnTransform: _factoriesAny.createColumnTransform
	};
	return dependenciesColumnTransform_generated;
}

var dependenciesCombinationsWithRep_generated = {};

var hasRequiredDependenciesCombinationsWithRep_generated;

function requireDependenciesCombinationsWithRep_generated () {
	if (hasRequiredDependenciesCombinationsWithRep_generated) return dependenciesCombinationsWithRep_generated;
	hasRequiredDependenciesCombinationsWithRep_generated = 1;

	Object.defineProperty(dependenciesCombinationsWithRep_generated, "__esModule", {
	  value: true
	});
	dependenciesCombinationsWithRep_generated.combinationsWithRepDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCombinationsWithRep_generated.combinationsWithRepDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCombinationsWithRep: _factoriesAny.createCombinationsWithRep
	};
	return dependenciesCombinationsWithRep_generated;
}

var dependenciesCompare_generated = {};

var hasRequiredDependenciesCompare_generated;

function requireDependenciesCompare_generated () {
	if (hasRequiredDependenciesCompare_generated) return dependenciesCompare_generated;
	hasRequiredDependenciesCompare_generated = 1;

	Object.defineProperty(dependenciesCompare_generated, "__esModule", {
	  value: true
	});
	dependenciesCompare_generated.compareDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCompare_generated.compareDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCompare: _factoriesAny.createCompare
	};
	return dependenciesCompare_generated;
}

var dependenciesCompareNatural_generated = {};

var hasRequiredDependenciesCompareNatural_generated;

function requireDependenciesCompareNatural_generated () {
	if (hasRequiredDependenciesCompareNatural_generated) return dependenciesCompareNatural_generated;
	hasRequiredDependenciesCompareNatural_generated = 1;

	Object.defineProperty(dependenciesCompareNatural_generated, "__esModule", {
	  value: true
	});
	dependenciesCompareNatural_generated.compareNaturalDependencies = void 0;
	var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCompareNatural_generated.compareNaturalDependencies = {
	  compareDependencies: _dependenciesCompareGenerated.compareDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCompareNatural: _factoriesAny.createCompareNatural
	};
	return dependenciesCompareNatural_generated;
}

var dependenciesCompareText_generated = {};

var hasRequiredDependenciesCompareText_generated;

function requireDependenciesCompareText_generated () {
	if (hasRequiredDependenciesCompareText_generated) return dependenciesCompareText_generated;
	hasRequiredDependenciesCompareText_generated = 1;

	Object.defineProperty(dependenciesCompareText_generated, "__esModule", {
	  value: true
	});
	dependenciesCompareText_generated.compareTextDependencies = void 0;
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCompareText_generated.compareTextDependencies = {
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCompareText: _factoriesAny.createCompareText
	};
	return dependenciesCompareText_generated;
}

var dependenciesCompile_generated = {};

var dependenciesParse_generated = {};

var dependenciesConditionalNode_generated = {};

var hasRequiredDependenciesConditionalNode_generated;

function requireDependenciesConditionalNode_generated () {
	if (hasRequiredDependenciesConditionalNode_generated) return dependenciesConditionalNode_generated;
	hasRequiredDependenciesConditionalNode_generated = 1;

	Object.defineProperty(dependenciesConditionalNode_generated, "__esModule", {
	  value: true
	});
	dependenciesConditionalNode_generated.ConditionalNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesConditionalNode_generated.ConditionalNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createConditionalNode: _factoriesAny.createConditionalNode
	};
	return dependenciesConditionalNode_generated;
}

var dependenciesConstantNode_generated = {};

var dependenciesIsBounded_generated = {};

var hasRequiredDependenciesIsBounded_generated;

function requireDependenciesIsBounded_generated () {
	if (hasRequiredDependenciesIsBounded_generated) return dependenciesIsBounded_generated;
	hasRequiredDependenciesIsBounded_generated = 1;

	Object.defineProperty(dependenciesIsBounded_generated, "__esModule", {
	  value: true
	});
	dependenciesIsBounded_generated.isBoundedDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsBounded_generated.isBoundedDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsBounded: _factoriesAny.createIsBounded
	};
	return dependenciesIsBounded_generated;
}

var hasRequiredDependenciesConstantNode_generated;

function requireDependenciesConstantNode_generated () {
	if (hasRequiredDependenciesConstantNode_generated) return dependenciesConstantNode_generated;
	hasRequiredDependenciesConstantNode_generated = 1;

	Object.defineProperty(dependenciesConstantNode_generated, "__esModule", {
	  value: true
	});
	dependenciesConstantNode_generated.ConstantNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesIsBoundedGenerated = requireDependenciesIsBounded_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesConstantNode_generated.ConstantNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  isBoundedDependencies: _dependenciesIsBoundedGenerated.isBoundedDependencies,
	  createConstantNode: _factoriesAny.createConstantNode
	};
	return dependenciesConstantNode_generated;
}

var dependenciesFunctionAssignmentNode_generated = {};

var hasRequiredDependenciesFunctionAssignmentNode_generated;

function requireDependenciesFunctionAssignmentNode_generated () {
	if (hasRequiredDependenciesFunctionAssignmentNode_generated) return dependenciesFunctionAssignmentNode_generated;
	hasRequiredDependenciesFunctionAssignmentNode_generated = 1;

	Object.defineProperty(dependenciesFunctionAssignmentNode_generated, "__esModule", {
	  value: true
	});
	dependenciesFunctionAssignmentNode_generated.FunctionAssignmentNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFunctionAssignmentNode_generated.FunctionAssignmentNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFunctionAssignmentNode: _factoriesAny.createFunctionAssignmentNode
	};
	return dependenciesFunctionAssignmentNode_generated;
}

var dependenciesFunctionNode_generated = {};

var dependenciesSymbolNode_generated = {};

var hasRequiredDependenciesSymbolNode_generated;

function requireDependenciesSymbolNode_generated () {
	if (hasRequiredDependenciesSymbolNode_generated) return dependenciesSymbolNode_generated;
	hasRequiredDependenciesSymbolNode_generated = 1;

	Object.defineProperty(dependenciesSymbolNode_generated, "__esModule", {
	  value: true
	});
	dependenciesSymbolNode_generated.SymbolNodeDependencies = void 0;
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSymbolNode_generated.SymbolNodeDependencies = {
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createSymbolNode: _factoriesAny.createSymbolNode
	};
	return dependenciesSymbolNode_generated;
}

var hasRequiredDependenciesFunctionNode_generated;

function requireDependenciesFunctionNode_generated () {
	if (hasRequiredDependenciesFunctionNode_generated) return dependenciesFunctionNode_generated;
	hasRequiredDependenciesFunctionNode_generated = 1;

	Object.defineProperty(dependenciesFunctionNode_generated, "__esModule", {
	  value: true
	});
	dependenciesFunctionNode_generated.FunctionNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFunctionNode_generated.FunctionNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  createFunctionNode: _factoriesAny.createFunctionNode
	};
	return dependenciesFunctionNode_generated;
}

var dependenciesIndexNode_generated = {};

var hasRequiredDependenciesIndexNode_generated;

function requireDependenciesIndexNode_generated () {
	if (hasRequiredDependenciesIndexNode_generated) return dependenciesIndexNode_generated;
	hasRequiredDependenciesIndexNode_generated = 1;

	Object.defineProperty(dependenciesIndexNode_generated, "__esModule", {
	  value: true
	});
	dependenciesIndexNode_generated.IndexNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIndexNode_generated.IndexNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  createIndexNode: _factoriesAny.createIndexNode
	};
	return dependenciesIndexNode_generated;
}

var dependenciesObjectNode_generated = {};

var hasRequiredDependenciesObjectNode_generated;

function requireDependenciesObjectNode_generated () {
	if (hasRequiredDependenciesObjectNode_generated) return dependenciesObjectNode_generated;
	hasRequiredDependenciesObjectNode_generated = 1;

	Object.defineProperty(dependenciesObjectNode_generated, "__esModule", {
	  value: true
	});
	dependenciesObjectNode_generated.ObjectNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesObjectNode_generated.ObjectNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createObjectNode: _factoriesAny.createObjectNode
	};
	return dependenciesObjectNode_generated;
}

var dependenciesOperatorNode_generated = {};

var hasRequiredDependenciesOperatorNode_generated;

function requireDependenciesOperatorNode_generated () {
	if (hasRequiredDependenciesOperatorNode_generated) return dependenciesOperatorNode_generated;
	hasRequiredDependenciesOperatorNode_generated = 1;

	Object.defineProperty(dependenciesOperatorNode_generated, "__esModule", {
	  value: true
	});
	dependenciesOperatorNode_generated.OperatorNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesOperatorNode_generated.OperatorNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createOperatorNode: _factoriesAny.createOperatorNode
	};
	return dependenciesOperatorNode_generated;
}

var dependenciesParenthesisNode_generated = {};

var hasRequiredDependenciesParenthesisNode_generated;

function requireDependenciesParenthesisNode_generated () {
	if (hasRequiredDependenciesParenthesisNode_generated) return dependenciesParenthesisNode_generated;
	hasRequiredDependenciesParenthesisNode_generated = 1;

	Object.defineProperty(dependenciesParenthesisNode_generated, "__esModule", {
	  value: true
	});
	dependenciesParenthesisNode_generated.ParenthesisNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesParenthesisNode_generated.ParenthesisNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createParenthesisNode: _factoriesAny.createParenthesisNode
	};
	return dependenciesParenthesisNode_generated;
}

var dependenciesRangeNode_generated = {};

var hasRequiredDependenciesRangeNode_generated;

function requireDependenciesRangeNode_generated () {
	if (hasRequiredDependenciesRangeNode_generated) return dependenciesRangeNode_generated;
	hasRequiredDependenciesRangeNode_generated = 1;

	Object.defineProperty(dependenciesRangeNode_generated, "__esModule", {
	  value: true
	});
	dependenciesRangeNode_generated.RangeNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRangeNode_generated.RangeNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createRangeNode: _factoriesAny.createRangeNode
	};
	return dependenciesRangeNode_generated;
}

var dependenciesRelationalNode_generated = {};

var hasRequiredDependenciesRelationalNode_generated;

function requireDependenciesRelationalNode_generated () {
	if (hasRequiredDependenciesRelationalNode_generated) return dependenciesRelationalNode_generated;
	hasRequiredDependenciesRelationalNode_generated = 1;

	Object.defineProperty(dependenciesRelationalNode_generated, "__esModule", {
	  value: true
	});
	dependenciesRelationalNode_generated.RelationalNodeDependencies = void 0;
	var _dependenciesNodeGenerated = requireDependenciesNode_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRelationalNode_generated.RelationalNodeDependencies = {
	  NodeDependencies: _dependenciesNodeGenerated.NodeDependencies,
	  createRelationalNode: _factoriesAny.createRelationalNode
	};
	return dependenciesRelationalNode_generated;
}

var hasRequiredDependenciesParse_generated;

function requireDependenciesParse_generated () {
	if (hasRequiredDependenciesParse_generated) return dependenciesParse_generated;
	hasRequiredDependenciesParse_generated = 1;

	Object.defineProperty(dependenciesParse_generated, "__esModule", {
	  value: true
	});
	dependenciesParse_generated.parseDependencies = void 0;
	var _dependenciesAccessorNodeGenerated = requireDependenciesAccessorNode_generated();
	var _dependenciesArrayNodeGenerated = requireDependenciesArrayNode_generated();
	var _dependenciesAssignmentNodeGenerated = requireDependenciesAssignmentNode_generated();
	var _dependenciesBlockNodeGenerated = requireDependenciesBlockNode_generated();
	var _dependenciesConditionalNodeGenerated = requireDependenciesConditionalNode_generated();
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionAssignmentNodeGenerated = requireDependenciesFunctionAssignmentNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesIndexNodeGenerated = requireDependenciesIndexNode_generated();
	var _dependenciesObjectNodeGenerated = requireDependenciesObjectNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
	var _dependenciesRangeNodeGenerated = requireDependenciesRangeNode_generated();
	var _dependenciesRelationalNodeGenerated = requireDependenciesRelationalNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesParse_generated.parseDependencies = {
	  AccessorNodeDependencies: _dependenciesAccessorNodeGenerated.AccessorNodeDependencies,
	  ArrayNodeDependencies: _dependenciesArrayNodeGenerated.ArrayNodeDependencies,
	  AssignmentNodeDependencies: _dependenciesAssignmentNodeGenerated.AssignmentNodeDependencies,
	  BlockNodeDependencies: _dependenciesBlockNodeGenerated.BlockNodeDependencies,
	  ConditionalNodeDependencies: _dependenciesConditionalNodeGenerated.ConditionalNodeDependencies,
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionAssignmentNodeDependencies: _dependenciesFunctionAssignmentNodeGenerated.FunctionAssignmentNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  IndexNodeDependencies: _dependenciesIndexNodeGenerated.IndexNodeDependencies,
	  ObjectNodeDependencies: _dependenciesObjectNodeGenerated.ObjectNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  ParenthesisNodeDependencies: _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies,
	  RangeNodeDependencies: _dependenciesRangeNodeGenerated.RangeNodeDependencies,
	  RelationalNodeDependencies: _dependenciesRelationalNodeGenerated.RelationalNodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createParse: _factoriesAny.createParse
	};
	return dependenciesParse_generated;
}

var hasRequiredDependenciesCompile_generated;

function requireDependenciesCompile_generated () {
	if (hasRequiredDependenciesCompile_generated) return dependenciesCompile_generated;
	hasRequiredDependenciesCompile_generated = 1;

	Object.defineProperty(dependenciesCompile_generated, "__esModule", {
	  value: true
	});
	dependenciesCompile_generated.compileDependencies = void 0;
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCompile_generated.compileDependencies = {
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCompile: _factoriesAny.createCompile
	};
	return dependenciesCompile_generated;
}

var dependenciesComplex_generated = {};

var hasRequiredDependenciesComplex_generated;

function requireDependenciesComplex_generated () {
	if (hasRequiredDependenciesComplex_generated) return dependenciesComplex_generated;
	hasRequiredDependenciesComplex_generated = 1;

	Object.defineProperty(dependenciesComplex_generated, "__esModule", {
	  value: true
	});
	dependenciesComplex_generated.complexDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesComplex_generated.complexDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createComplex: _factoriesAny.createComplex
	};
	return dependenciesComplex_generated;
}

var dependenciesComposition_generated = {};

var hasRequiredDependenciesComposition_generated;

function requireDependenciesComposition_generated () {
	if (hasRequiredDependenciesComposition_generated) return dependenciesComposition_generated;
	hasRequiredDependenciesComposition_generated = 1;

	Object.defineProperty(dependenciesComposition_generated, "__esModule", {
	  value: true
	});
	dependenciesComposition_generated.compositionDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesCombinationsGenerated = requireDependenciesCombinations_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesComposition_generated.compositionDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  combinationsDependencies: _dependenciesCombinationsGenerated.combinationsDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  isPositiveDependencies: _dependenciesIsPositiveGenerated.isPositiveDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createComposition: _factoriesAny.createComposition
	};
	return dependenciesComposition_generated;
}

var dependenciesConcatTransform_generated = {};

var hasRequiredDependenciesConcatTransform_generated;

function requireDependenciesConcatTransform_generated () {
	if (hasRequiredDependenciesConcatTransform_generated) return dependenciesConcatTransform_generated;
	hasRequiredDependenciesConcatTransform_generated = 1;

	Object.defineProperty(dependenciesConcatTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesConcatTransform_generated.concatTransformDependencies = void 0;
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesConcatTransform_generated.concatTransformDependencies = {
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createConcatTransform: _factoriesAny.createConcatTransform
	};
	return dependenciesConcatTransform_generated;
}

var dependenciesConductanceQuantum_generated = {};

var hasRequiredDependenciesConductanceQuantum_generated;

function requireDependenciesConductanceQuantum_generated () {
	if (hasRequiredDependenciesConductanceQuantum_generated) return dependenciesConductanceQuantum_generated;
	hasRequiredDependenciesConductanceQuantum_generated = 1;

	Object.defineProperty(dependenciesConductanceQuantum_generated, "__esModule", {
	  value: true
	});
	dependenciesConductanceQuantum_generated.conductanceQuantumDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesConductanceQuantum_generated.conductanceQuantumDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createConductanceQuantum: _factoriesAny.createConductanceQuantum
	};
	return dependenciesConductanceQuantum_generated;
}

var dependenciesCorr_generated = {};

var dependenciesDivide_generated = {};

var hasRequiredDependenciesDivide_generated;

function requireDependenciesDivide_generated () {
	if (hasRequiredDependenciesDivide_generated) return dependenciesDivide_generated;
	hasRequiredDependenciesDivide_generated = 1;

	Object.defineProperty(dependenciesDivide_generated, "__esModule", {
	  value: true
	});
	dependenciesDivide_generated.divideDependencies = void 0;
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesInvGenerated = requireDependenciesInv_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDivide_generated.divideDependencies = {
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  invDependencies: _dependenciesInvGenerated.invDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDivide: _factoriesAny.createDivide
	};
	return dependenciesDivide_generated;
}

var dependenciesMean_generated = {};

var hasRequiredDependenciesMean_generated;

function requireDependenciesMean_generated () {
	if (hasRequiredDependenciesMean_generated) return dependenciesMean_generated;
	hasRequiredDependenciesMean_generated = 1;

	Object.defineProperty(dependenciesMean_generated, "__esModule", {
	  value: true
	});
	dependenciesMean_generated.meanDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMean_generated.meanDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMean: _factoriesAny.createMean
	};
	return dependenciesMean_generated;
}

var dependenciesSqrt_generated = {};

var hasRequiredDependenciesSqrt_generated;

function requireDependenciesSqrt_generated () {
	if (hasRequiredDependenciesSqrt_generated) return dependenciesSqrt_generated;
	hasRequiredDependenciesSqrt_generated = 1;

	Object.defineProperty(dependenciesSqrt_generated, "__esModule", {
	  value: true
	});
	dependenciesSqrt_generated.sqrtDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSqrt_generated.sqrtDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSqrt: _factoriesAny.createSqrt
	};
	return dependenciesSqrt_generated;
}

var dependenciesSubtract_generated = {};

var hasRequiredDependenciesSubtract_generated;

function requireDependenciesSubtract_generated () {
	if (hasRequiredDependenciesSubtract_generated) return dependenciesSubtract_generated;
	hasRequiredDependenciesSubtract_generated = 1;

	Object.defineProperty(dependenciesSubtract_generated, "__esModule", {
	  value: true
	});
	dependenciesSubtract_generated.subtractDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSubtract_generated.subtractDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createSubtract: _factoriesAny.createSubtract
	};
	return dependenciesSubtract_generated;
}

var dependenciesSum_generated = {};

var hasRequiredDependenciesSum_generated;

function requireDependenciesSum_generated () {
	if (hasRequiredDependenciesSum_generated) return dependenciesSum_generated;
	hasRequiredDependenciesSum_generated = 1;

	Object.defineProperty(dependenciesSum_generated, "__esModule", {
	  value: true
	});
	dependenciesSum_generated.sumDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSum_generated.sumDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSum: _factoriesAny.createSum
	};
	return dependenciesSum_generated;
}

var hasRequiredDependenciesCorr_generated;

function requireDependenciesCorr_generated () {
	if (hasRequiredDependenciesCorr_generated) return dependenciesCorr_generated;
	hasRequiredDependenciesCorr_generated = 1;

	Object.defineProperty(dependenciesCorr_generated, "__esModule", {
	  value: true
	});
	dependenciesCorr_generated.corrDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMeanGenerated = requireDependenciesMean_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesSumGenerated = requireDependenciesSum_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCorr_generated.corrDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  meanDependencies: _dependenciesMeanGenerated.meanDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  sumDependencies: _dependenciesSumGenerated.sumDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCorr: _factoriesAny.createCorr
	};
	return dependenciesCorr_generated;
}

var dependenciesCos_generated = {};

var hasRequiredDependenciesCos_generated;

function requireDependenciesCos_generated () {
	if (hasRequiredDependenciesCos_generated) return dependenciesCos_generated;
	hasRequiredDependenciesCos_generated = 1;

	Object.defineProperty(dependenciesCos_generated, "__esModule", {
	  value: true
	});
	dependenciesCos_generated.cosDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCos_generated.cosDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCos: _factoriesAny.createCos
	};
	return dependenciesCos_generated;
}

var dependenciesCosh_generated = {};

var hasRequiredDependenciesCosh_generated;

function requireDependenciesCosh_generated () {
	if (hasRequiredDependenciesCosh_generated) return dependenciesCosh_generated;
	hasRequiredDependenciesCosh_generated = 1;

	Object.defineProperty(dependenciesCosh_generated, "__esModule", {
	  value: true
	});
	dependenciesCosh_generated.coshDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCosh_generated.coshDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCosh: _factoriesAny.createCosh
	};
	return dependenciesCosh_generated;
}

var dependenciesCot_generated = {};

var hasRequiredDependenciesCot_generated;

function requireDependenciesCot_generated () {
	if (hasRequiredDependenciesCot_generated) return dependenciesCot_generated;
	hasRequiredDependenciesCot_generated = 1;

	Object.defineProperty(dependenciesCot_generated, "__esModule", {
	  value: true
	});
	dependenciesCot_generated.cotDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCot_generated.cotDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCot: _factoriesAny.createCot
	};
	return dependenciesCot_generated;
}

var dependenciesCoth_generated = {};

var hasRequiredDependenciesCoth_generated;

function requireDependenciesCoth_generated () {
	if (hasRequiredDependenciesCoth_generated) return dependenciesCoth_generated;
	hasRequiredDependenciesCoth_generated = 1;

	Object.defineProperty(dependenciesCoth_generated, "__esModule", {
	  value: true
	});
	dependenciesCoth_generated.cothDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCoth_generated.cothDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCoth: _factoriesAny.createCoth
	};
	return dependenciesCoth_generated;
}

var dependenciesCoulomb_generated = {};

var hasRequiredDependenciesCoulomb_generated;

function requireDependenciesCoulomb_generated () {
	if (hasRequiredDependenciesCoulomb_generated) return dependenciesCoulomb_generated;
	hasRequiredDependenciesCoulomb_generated = 1;

	Object.defineProperty(dependenciesCoulomb_generated, "__esModule", {
	  value: true
	});
	dependenciesCoulomb_generated.coulombDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCoulomb_generated.coulombDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createCoulomb: _factoriesAny.createCoulomb
	};
	return dependenciesCoulomb_generated;
}

var dependenciesCoulombConstant_generated = {};

var hasRequiredDependenciesCoulombConstant_generated;

function requireDependenciesCoulombConstant_generated () {
	if (hasRequiredDependenciesCoulombConstant_generated) return dependenciesCoulombConstant_generated;
	hasRequiredDependenciesCoulombConstant_generated = 1;

	Object.defineProperty(dependenciesCoulombConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesCoulombConstant_generated.coulombConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCoulombConstant_generated.coulombConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createCoulombConstant: _factoriesAny.createCoulombConstant
	};
	return dependenciesCoulombConstant_generated;
}

var dependenciesCount_generated = {};

var dependenciesProd_generated = {};

var hasRequiredDependenciesProd_generated;

function requireDependenciesProd_generated () {
	if (hasRequiredDependenciesProd_generated) return dependenciesProd_generated;
	hasRequiredDependenciesProd_generated = 1;

	Object.defineProperty(dependenciesProd_generated, "__esModule", {
	  value: true
	});
	dependenciesProd_generated.prodDependencies = void 0;
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesProd_generated.prodDependencies = {
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createProd: _factoriesAny.createProd
	};
	return dependenciesProd_generated;
}

var hasRequiredDependenciesCount_generated;

function requireDependenciesCount_generated () {
	if (hasRequiredDependenciesCount_generated) return dependenciesCount_generated;
	hasRequiredDependenciesCount_generated = 1;

	Object.defineProperty(dependenciesCount_generated, "__esModule", {
	  value: true
	});
	dependenciesCount_generated.countDependencies = void 0;
	var _dependenciesProdGenerated = requireDependenciesProd_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCount_generated.countDependencies = {
	  prodDependencies: _dependenciesProdGenerated.prodDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCount: _factoriesAny.createCount
	};
	return dependenciesCount_generated;
}

var dependenciesCreateUnit_generated = {};

var hasRequiredDependenciesCreateUnit_generated;

function requireDependenciesCreateUnit_generated () {
	if (hasRequiredDependenciesCreateUnit_generated) return dependenciesCreateUnit_generated;
	hasRequiredDependenciesCreateUnit_generated = 1;

	Object.defineProperty(dependenciesCreateUnit_generated, "__esModule", {
	  value: true
	});
	dependenciesCreateUnit_generated.createUnitDependencies = void 0;
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCreateUnit_generated.createUnitDependencies = {
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCreateUnit: _factoriesAny.createCreateUnit
	};
	return dependenciesCreateUnit_generated;
}

var dependenciesCross_generated = {};

var hasRequiredDependenciesCross_generated;

function requireDependenciesCross_generated () {
	if (hasRequiredDependenciesCross_generated) return dependenciesCross_generated;
	hasRequiredDependenciesCross_generated = 1;

	Object.defineProperty(dependenciesCross_generated, "__esModule", {
	  value: true
	});
	dependenciesCross_generated.crossDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCross_generated.crossDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCross: _factoriesAny.createCross
	};
	return dependenciesCross_generated;
}

var dependenciesCsc_generated = {};

var hasRequiredDependenciesCsc_generated;

function requireDependenciesCsc_generated () {
	if (hasRequiredDependenciesCsc_generated) return dependenciesCsc_generated;
	hasRequiredDependenciesCsc_generated = 1;

	Object.defineProperty(dependenciesCsc_generated, "__esModule", {
	  value: true
	});
	dependenciesCsc_generated.cscDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCsc_generated.cscDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCsc: _factoriesAny.createCsc
	};
	return dependenciesCsc_generated;
}

var dependenciesCsch_generated = {};

var hasRequiredDependenciesCsch_generated;

function requireDependenciesCsch_generated () {
	if (hasRequiredDependenciesCsch_generated) return dependenciesCsch_generated;
	hasRequiredDependenciesCsch_generated = 1;

	Object.defineProperty(dependenciesCsch_generated, "__esModule", {
	  value: true
	});
	dependenciesCsch_generated.cschDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCsch_generated.cschDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCsch: _factoriesAny.createCsch
	};
	return dependenciesCsch_generated;
}

var dependenciesCtranspose_generated = {};

var dependenciesTranspose_generated = {};

var hasRequiredDependenciesTranspose_generated;

function requireDependenciesTranspose_generated () {
	if (hasRequiredDependenciesTranspose_generated) return dependenciesTranspose_generated;
	hasRequiredDependenciesTranspose_generated = 1;

	Object.defineProperty(dependenciesTranspose_generated, "__esModule", {
	  value: true
	});
	dependenciesTranspose_generated.transposeDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTranspose_generated.transposeDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createTranspose: _factoriesAny.createTranspose
	};
	return dependenciesTranspose_generated;
}

var hasRequiredDependenciesCtranspose_generated;

function requireDependenciesCtranspose_generated () {
	if (hasRequiredDependenciesCtranspose_generated) return dependenciesCtranspose_generated;
	hasRequiredDependenciesCtranspose_generated = 1;

	Object.defineProperty(dependenciesCtranspose_generated, "__esModule", {
	  value: true
	});
	dependenciesCtranspose_generated.ctransposeDependencies = void 0;
	var _dependenciesConjGenerated = requireDependenciesConj_generated();
	var _dependenciesTransposeGenerated = requireDependenciesTranspose_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCtranspose_generated.ctransposeDependencies = {
	  conjDependencies: _dependenciesConjGenerated.conjDependencies,
	  transposeDependencies: _dependenciesTransposeGenerated.transposeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCtranspose: _factoriesAny.createCtranspose
	};
	return dependenciesCtranspose_generated;
}

var dependenciesCube_generated = {};

var hasRequiredDependenciesCube_generated;

function requireDependenciesCube_generated () {
	if (hasRequiredDependenciesCube_generated) return dependenciesCube_generated;
	hasRequiredDependenciesCube_generated = 1;

	Object.defineProperty(dependenciesCube_generated, "__esModule", {
	  value: true
	});
	dependenciesCube_generated.cubeDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCube_generated.cubeDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createCube: _factoriesAny.createCube
	};
	return dependenciesCube_generated;
}

var dependenciesCumSum_generated = {};

var dependenciesUnaryPlus_generated = {};

var hasRequiredDependenciesUnaryPlus_generated;

function requireDependenciesUnaryPlus_generated () {
	if (hasRequiredDependenciesUnaryPlus_generated) return dependenciesUnaryPlus_generated;
	hasRequiredDependenciesUnaryPlus_generated = 1;

	Object.defineProperty(dependenciesUnaryPlus_generated, "__esModule", {
	  value: true
	});
	dependenciesUnaryPlus_generated.unaryPlusDependencies = void 0;
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUnaryPlus_generated.unaryPlusDependencies = {
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createUnaryPlus: _factoriesAny.createUnaryPlus
	};
	return dependenciesUnaryPlus_generated;
}

var hasRequiredDependenciesCumSum_generated;

function requireDependenciesCumSum_generated () {
	if (hasRequiredDependenciesCumSum_generated) return dependenciesCumSum_generated;
	hasRequiredDependenciesCumSum_generated = 1;

	Object.defineProperty(dependenciesCumSum_generated, "__esModule", {
	  value: true
	});
	dependenciesCumSum_generated.cumsumDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryPlusGenerated = requireDependenciesUnaryPlus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCumSum_generated.cumsumDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryPlusDependencies: _dependenciesUnaryPlusGenerated.unaryPlusDependencies,
	  createCumSum: _factoriesAny.createCumSum
	};
	return dependenciesCumSum_generated;
}

var dependenciesCumSumTransform_generated = {};

var hasRequiredDependenciesCumSumTransform_generated;

function requireDependenciesCumSumTransform_generated () {
	if (hasRequiredDependenciesCumSumTransform_generated) return dependenciesCumSumTransform_generated;
	hasRequiredDependenciesCumSumTransform_generated = 1;

	Object.defineProperty(dependenciesCumSumTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesCumSumTransform_generated.cumsumTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryPlusGenerated = requireDependenciesUnaryPlus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesCumSumTransform_generated.cumsumTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryPlusDependencies: _dependenciesUnaryPlusGenerated.unaryPlusDependencies,
	  createCumSumTransform: _factoriesAny.createCumSumTransform
	};
	return dependenciesCumSumTransform_generated;
}

var dependenciesDeepEqual_generated = {};

var hasRequiredDependenciesDeepEqual_generated;

function requireDependenciesDeepEqual_generated () {
	if (hasRequiredDependenciesDeepEqual_generated) return dependenciesDeepEqual_generated;
	hasRequiredDependenciesDeepEqual_generated = 1;

	Object.defineProperty(dependenciesDeepEqual_generated, "__esModule", {
	  value: true
	});
	dependenciesDeepEqual_generated.deepEqualDependencies = void 0;
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDeepEqual_generated.deepEqualDependencies = {
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDeepEqual: _factoriesAny.createDeepEqual
	};
	return dependenciesDeepEqual_generated;
}

var dependenciesDen_generated = {};

var hasRequiredDependenciesDen_generated;

function requireDependenciesDen_generated () {
	if (hasRequiredDependenciesDen_generated) return dependenciesDen_generated;
	hasRequiredDependenciesDen_generated = 1;

	Object.defineProperty(dependenciesDen_generated, "__esModule", {
	  value: true
	});
	dependenciesDen_generated.denDependencies = void 0;
	var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDen_generated.denDependencies = {
	  fractionDependencies: _dependenciesFractionGenerated.fractionDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDen: _factoriesAny.createDen
	};
	return dependenciesDen_generated;
}

var dependenciesDerivative_generated = {};

var dependenciesSimplify_generated = {};

var dependenciesReplacer_generated = {};

var hasRequiredDependenciesReplacer_generated;

function requireDependenciesReplacer_generated () {
	if (hasRequiredDependenciesReplacer_generated) return dependenciesReplacer_generated;
	hasRequiredDependenciesReplacer_generated = 1;

	Object.defineProperty(dependenciesReplacer_generated, "__esModule", {
	  value: true
	});
	dependenciesReplacer_generated.replacerDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesReplacer_generated.replacerDependencies = {
	  createReplacer: _factoriesAny.createReplacer
	};
	return dependenciesReplacer_generated;
}

var dependenciesResolve_generated = {};

var hasRequiredDependenciesResolve_generated;

function requireDependenciesResolve_generated () {
	if (hasRequiredDependenciesResolve_generated) return dependenciesResolve_generated;
	hasRequiredDependenciesResolve_generated = 1;

	Object.defineProperty(dependenciesResolve_generated, "__esModule", {
	  value: true
	});
	dependenciesResolve_generated.resolveDependencies = void 0;
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesResolve_generated.resolveDependencies = {
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  ParenthesisNodeDependencies: _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createResolve: _factoriesAny.createResolve
	};
	return dependenciesResolve_generated;
}

var dependenciesSimplifyConstant_generated = {};

var hasRequiredDependenciesSimplifyConstant_generated;

function requireDependenciesSimplifyConstant_generated () {
	if (hasRequiredDependenciesSimplifyConstant_generated) return dependenciesSimplifyConstant_generated;
	hasRequiredDependenciesSimplifyConstant_generated = 1;

	Object.defineProperty(dependenciesSimplifyConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesSimplifyConstant_generated.simplifyConstantDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
	var _dependenciesAccessorNodeGenerated = requireDependenciesAccessorNode_generated();
	var _dependenciesArrayNodeGenerated = requireDependenciesArrayNode_generated();
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesIndexNodeGenerated = requireDependenciesIndexNode_generated();
	var _dependenciesObjectNodeGenerated = requireDependenciesObjectNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _dependenciesIsBoundedGenerated = requireDependenciesIsBounded_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSimplifyConstant_generated.simplifyConstantDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  fractionDependencies: _dependenciesFractionGenerated.fractionDependencies,
	  AccessorNodeDependencies: _dependenciesAccessorNodeGenerated.AccessorNodeDependencies,
	  ArrayNodeDependencies: _dependenciesArrayNodeGenerated.ArrayNodeDependencies,
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  IndexNodeDependencies: _dependenciesIndexNodeGenerated.IndexNodeDependencies,
	  ObjectNodeDependencies: _dependenciesObjectNodeGenerated.ObjectNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  isBoundedDependencies: _dependenciesIsBoundedGenerated.isBoundedDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSimplifyConstant: _factoriesAny.createSimplifyConstant
	};
	return dependenciesSimplifyConstant_generated;
}

var dependenciesSimplifyCore_generated = {};

var hasRequiredDependenciesSimplifyCore_generated;

function requireDependenciesSimplifyCore_generated () {
	if (hasRequiredDependenciesSimplifyCore_generated) return dependenciesSimplifyCore_generated;
	hasRequiredDependenciesSimplifyCore_generated = 1;

	Object.defineProperty(dependenciesSimplifyCore_generated, "__esModule", {
	  value: true
	});
	dependenciesSimplifyCore_generated.simplifyCoreDependencies = void 0;
	var _dependenciesAccessorNodeGenerated = requireDependenciesAccessorNode_generated();
	var _dependenciesArrayNodeGenerated = requireDependenciesArrayNode_generated();
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesIndexNodeGenerated = requireDependenciesIndexNode_generated();
	var _dependenciesObjectNodeGenerated = requireDependenciesObjectNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSimplifyCore_generated.simplifyCoreDependencies = {
	  AccessorNodeDependencies: _dependenciesAccessorNodeGenerated.AccessorNodeDependencies,
	  ArrayNodeDependencies: _dependenciesArrayNodeGenerated.ArrayNodeDependencies,
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  IndexNodeDependencies: _dependenciesIndexNodeGenerated.IndexNodeDependencies,
	  ObjectNodeDependencies: _dependenciesObjectNodeGenerated.ObjectNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  ParenthesisNodeDependencies: _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSimplifyCore: _factoriesAny.createSimplifyCore
	};
	return dependenciesSimplifyCore_generated;
}

var hasRequiredDependenciesSimplify_generated;

function requireDependenciesSimplify_generated () {
	if (hasRequiredDependenciesSimplify_generated) return dependenciesSimplify_generated;
	hasRequiredDependenciesSimplify_generated = 1;

	Object.defineProperty(dependenciesSimplify_generated, "__esModule", {
	  value: true
	});
	dependenciesSimplify_generated.simplifyDependencies = void 0;
	var _dependenciesAccessorNodeGenerated = requireDependenciesAccessorNode_generated();
	var _dependenciesArrayNodeGenerated = requireDependenciesArrayNode_generated();
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesIndexNodeGenerated = requireDependenciesIndexNode_generated();
	var _dependenciesObjectNodeGenerated = requireDependenciesObjectNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesReplacerGenerated = requireDependenciesReplacer_generated();
	var _dependenciesResolveGenerated = requireDependenciesResolve_generated();
	var _dependenciesSimplifyConstantGenerated = requireDependenciesSimplifyConstant_generated();
	var _dependenciesSimplifyCoreGenerated = requireDependenciesSimplifyCore_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSimplify_generated.simplifyDependencies = {
	  AccessorNodeDependencies: _dependenciesAccessorNodeGenerated.AccessorNodeDependencies,
	  ArrayNodeDependencies: _dependenciesArrayNodeGenerated.ArrayNodeDependencies,
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  IndexNodeDependencies: _dependenciesIndexNodeGenerated.IndexNodeDependencies,
	  ObjectNodeDependencies: _dependenciesObjectNodeGenerated.ObjectNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  ParenthesisNodeDependencies: _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  replacerDependencies: _dependenciesReplacerGenerated.replacerDependencies,
	  resolveDependencies: _dependenciesResolveGenerated.resolveDependencies,
	  simplifyConstantDependencies: _dependenciesSimplifyConstantGenerated.simplifyConstantDependencies,
	  simplifyCoreDependencies: _dependenciesSimplifyCoreGenerated.simplifyCoreDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSimplify: _factoriesAny.createSimplify
	};
	return dependenciesSimplify_generated;
}

var hasRequiredDependenciesDerivative_generated;

function requireDependenciesDerivative_generated () {
	if (hasRequiredDependenciesDerivative_generated) return dependenciesDerivative_generated;
	hasRequiredDependenciesDerivative_generated = 1;

	Object.defineProperty(dependenciesDerivative_generated, "__esModule", {
	  value: true
	});
	dependenciesDerivative_generated.derivativeDependencies = void 0;
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesSimplifyGenerated = requireDependenciesSimplify_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDerivative_generated.derivativeDependencies = {
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  ParenthesisNodeDependencies: _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  simplifyDependencies: _dependenciesSimplifyGenerated.simplifyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDerivative: _factoriesAny.createDerivative
	};
	return dependenciesDerivative_generated;
}

var dependenciesDeuteronMass_generated = {};

var hasRequiredDependenciesDeuteronMass_generated;

function requireDependenciesDeuteronMass_generated () {
	if (hasRequiredDependenciesDeuteronMass_generated) return dependenciesDeuteronMass_generated;
	hasRequiredDependenciesDeuteronMass_generated = 1;

	Object.defineProperty(dependenciesDeuteronMass_generated, "__esModule", {
	  value: true
	});
	dependenciesDeuteronMass_generated.deuteronMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDeuteronMass_generated.deuteronMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createDeuteronMass: _factoriesAny.createDeuteronMass
	};
	return dependenciesDeuteronMass_generated;
}

var dependenciesDiag_generated = {};

var hasRequiredDependenciesDiag_generated;

function requireDependenciesDiag_generated () {
	if (hasRequiredDependenciesDiag_generated) return dependenciesDiag_generated;
	hasRequiredDependenciesDiag_generated = 1;

	Object.defineProperty(dependenciesDiag_generated, "__esModule", {
	  value: true
	});
	dependenciesDiag_generated.diagDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDiag_generated.diagDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDiag: _factoriesAny.createDiag
	};
	return dependenciesDiag_generated;
}

var dependenciesDiff_generated = {};

var hasRequiredDependenciesDiff_generated;

function requireDependenciesDiff_generated () {
	if (hasRequiredDependenciesDiff_generated) return dependenciesDiff_generated;
	hasRequiredDependenciesDiff_generated = 1;

	Object.defineProperty(dependenciesDiff_generated, "__esModule", {
	  value: true
	});
	dependenciesDiff_generated.diffDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDiff_generated.diffDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDiff: _factoriesAny.createDiff
	};
	return dependenciesDiff_generated;
}

var dependenciesDiffTransform_generated = {};

var hasRequiredDependenciesDiffTransform_generated;

function requireDependenciesDiffTransform_generated () {
	if (hasRequiredDependenciesDiffTransform_generated) return dependenciesDiffTransform_generated;
	hasRequiredDependenciesDiffTransform_generated = 1;

	Object.defineProperty(dependenciesDiffTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesDiffTransform_generated.diffTransformDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDiffTransform_generated.diffTransformDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDiffTransform: _factoriesAny.createDiffTransform
	};
	return dependenciesDiffTransform_generated;
}

var dependenciesDistance_generated = {};

var hasRequiredDependenciesDistance_generated;

function requireDependenciesDistance_generated () {
	if (hasRequiredDependenciesDistance_generated) return dependenciesDistance_generated;
	hasRequiredDependenciesDistance_generated = 1;

	Object.defineProperty(dependenciesDistance_generated, "__esModule", {
	  value: true
	});
	dependenciesDistance_generated.distanceDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDeepEqualGenerated = requireDependenciesDeepEqual_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDistance_generated.distanceDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  deepEqualDependencies: _dependenciesDeepEqualGenerated.deepEqualDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDistance: _factoriesAny.createDistance
	};
	return dependenciesDistance_generated;
}

var dependenciesDotDivide_generated = {};

var hasRequiredDependenciesDotDivide_generated;

function requireDependenciesDotDivide_generated () {
	if (hasRequiredDependenciesDotDivide_generated) return dependenciesDotDivide_generated;
	hasRequiredDependenciesDotDivide_generated = 1;

	Object.defineProperty(dependenciesDotDivide_generated, "__esModule", {
	  value: true
	});
	dependenciesDotDivide_generated.dotDivideDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDotDivide_generated.dotDivideDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDotDivide: _factoriesAny.createDotDivide
	};
	return dependenciesDotDivide_generated;
}

var dependenciesDotMultiply_generated = {};

var hasRequiredDependenciesDotMultiply_generated;

function requireDependenciesDotMultiply_generated () {
	if (hasRequiredDependenciesDotMultiply_generated) return dependenciesDotMultiply_generated;
	hasRequiredDependenciesDotMultiply_generated = 1;

	Object.defineProperty(dependenciesDotMultiply_generated, "__esModule", {
	  value: true
	});
	dependenciesDotMultiply_generated.dotMultiplyDependencies = void 0;
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDotMultiply_generated.dotMultiplyDependencies = {
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDotMultiply: _factoriesAny.createDotMultiply
	};
	return dependenciesDotMultiply_generated;
}

var dependenciesDotPow_generated = {};

var hasRequiredDependenciesDotPow_generated;

function requireDependenciesDotPow_generated () {
	if (hasRequiredDependenciesDotPow_generated) return dependenciesDotPow_generated;
	hasRequiredDependenciesDotPow_generated = 1;

	Object.defineProperty(dependenciesDotPow_generated, "__esModule", {
	  value: true
	});
	dependenciesDotPow_generated.dotPowDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesDotPow_generated.dotPowDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createDotPow: _factoriesAny.createDotPow
	};
	return dependenciesDotPow_generated;
}

var dependenciesE_generated = {};

var hasRequiredDependenciesE_generated;

function requireDependenciesE_generated () {
	if (hasRequiredDependenciesE_generated) return dependenciesE_generated;
	hasRequiredDependenciesE_generated = 1;

	Object.defineProperty(dependenciesE_generated, "__esModule", {
	  value: true
	});
	dependenciesE_generated.eDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesE_generated.eDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createE: _factoriesAny.createE
	};
	return dependenciesE_generated;
}

var dependenciesEfimovFactor_generated = {};

var hasRequiredDependenciesEfimovFactor_generated;

function requireDependenciesEfimovFactor_generated () {
	if (hasRequiredDependenciesEfimovFactor_generated) return dependenciesEfimovFactor_generated;
	hasRequiredDependenciesEfimovFactor_generated = 1;

	Object.defineProperty(dependenciesEfimovFactor_generated, "__esModule", {
	  value: true
	});
	dependenciesEfimovFactor_generated.efimovFactorDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesEfimovFactor_generated.efimovFactorDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createEfimovFactor: _factoriesAny.createEfimovFactor
	};
	return dependenciesEfimovFactor_generated;
}

var dependenciesEigs_generated = {};

var dependenciesFlatten_generated = {};

var hasRequiredDependenciesFlatten_generated;

function requireDependenciesFlatten_generated () {
	if (hasRequiredDependenciesFlatten_generated) return dependenciesFlatten_generated;
	hasRequiredDependenciesFlatten_generated = 1;

	Object.defineProperty(dependenciesFlatten_generated, "__esModule", {
	  value: true
	});
	dependenciesFlatten_generated.flattenDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFlatten_generated.flattenDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFlatten: _factoriesAny.createFlatten
	};
	return dependenciesFlatten_generated;
}

var dependenciesIm_generated = {};

var hasRequiredDependenciesIm_generated;

function requireDependenciesIm_generated () {
	if (hasRequiredDependenciesIm_generated) return dependenciesIm_generated;
	hasRequiredDependenciesIm_generated = 1;

	Object.defineProperty(dependenciesIm_generated, "__esModule", {
	  value: true
	});
	dependenciesIm_generated.imDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIm_generated.imDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIm: _factoriesAny.createIm
	};
	return dependenciesIm_generated;
}

var dependenciesMatrixFromColumns_generated = {};

var hasRequiredDependenciesMatrixFromColumns_generated;

function requireDependenciesMatrixFromColumns_generated () {
	if (hasRequiredDependenciesMatrixFromColumns_generated) return dependenciesMatrixFromColumns_generated;
	hasRequiredDependenciesMatrixFromColumns_generated = 1;

	Object.defineProperty(dependenciesMatrixFromColumns_generated, "__esModule", {
	  value: true
	});
	dependenciesMatrixFromColumns_generated.matrixFromColumnsDependencies = void 0;
	var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMatrixFromColumns_generated.matrixFromColumnsDependencies = {
	  flattenDependencies: _dependenciesFlattenGenerated.flattenDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMatrixFromColumns: _factoriesAny.createMatrixFromColumns
	};
	return dependenciesMatrixFromColumns_generated;
}

var dependenciesQr_generated = {};

var dependenciesSign_generated = {};

var hasRequiredDependenciesSign_generated;

function requireDependenciesSign_generated () {
	if (hasRequiredDependenciesSign_generated) return dependenciesSign_generated;
	hasRequiredDependenciesSign_generated = 1;

	Object.defineProperty(dependenciesSign_generated, "__esModule", {
	  value: true
	});
	dependenciesSign_generated.signDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
	var _dependenciesComplexGenerated = requireDependenciesComplex_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSign_generated.signDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  FractionDependencies: _dependenciesFractionClassGenerated.FractionDependencies,
	  complexDependencies: _dependenciesComplexGenerated.complexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSign: _factoriesAny.createSign
	};
	return dependenciesSign_generated;
}

var hasRequiredDependenciesQr_generated;

function requireDependenciesQr_generated () {
	if (hasRequiredDependenciesQr_generated) return dependenciesQr_generated;
	hasRequiredDependenciesQr_generated = 1;

	Object.defineProperty(dependenciesQr_generated, "__esModule", {
	  value: true
	});
	dependenciesQr_generated.qrDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesComplexGenerated = requireDependenciesComplex_generated();
	var _dependenciesConjGenerated = requireDependenciesConj_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSignGenerated = requireDependenciesSign_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesQr_generated.qrDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  complexDependencies: _dependenciesComplexGenerated.complexDependencies,
	  conjDependencies: _dependenciesConjGenerated.conjDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  signDependencies: _dependenciesSignGenerated.signDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createQr: _factoriesAny.createQr
	};
	return dependenciesQr_generated;
}

var dependenciesRe_generated = {};

var hasRequiredDependenciesRe_generated;

function requireDependenciesRe_generated () {
	if (hasRequiredDependenciesRe_generated) return dependenciesRe_generated;
	hasRequiredDependenciesRe_generated = 1;

	Object.defineProperty(dependenciesRe_generated, "__esModule", {
	  value: true
	});
	dependenciesRe_generated.reDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRe_generated.reDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRe: _factoriesAny.createRe
	};
	return dependenciesRe_generated;
}

var dependenciesReshape_generated = {};

var hasRequiredDependenciesReshape_generated;

function requireDependenciesReshape_generated () {
	if (hasRequiredDependenciesReshape_generated) return dependenciesReshape_generated;
	hasRequiredDependenciesReshape_generated = 1;

	Object.defineProperty(dependenciesReshape_generated, "__esModule", {
	  value: true
	});
	dependenciesReshape_generated.reshapeDependencies = void 0;
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesReshape_generated.reshapeDependencies = {
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createReshape: _factoriesAny.createReshape
	};
	return dependenciesReshape_generated;
}

var dependenciesSin_generated = {};

var hasRequiredDependenciesSin_generated;

function requireDependenciesSin_generated () {
	if (hasRequiredDependenciesSin_generated) return dependenciesSin_generated;
	hasRequiredDependenciesSin_generated = 1;

	Object.defineProperty(dependenciesSin_generated, "__esModule", {
	  value: true
	});
	dependenciesSin_generated.sinDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSin_generated.sinDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSin: _factoriesAny.createSin
	};
	return dependenciesSin_generated;
}

var dependenciesUsolve_generated = {};

var hasRequiredDependenciesUsolve_generated;

function requireDependenciesUsolve_generated () {
	if (hasRequiredDependenciesUsolve_generated) return dependenciesUsolve_generated;
	hasRequiredDependenciesUsolve_generated = 1;

	Object.defineProperty(dependenciesUsolve_generated, "__esModule", {
	  value: true
	});
	dependenciesUsolve_generated.usolveDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUsolve_generated.usolveDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createUsolve: _factoriesAny.createUsolve
	};
	return dependenciesUsolve_generated;
}

var dependenciesUsolveAll_generated = {};

var hasRequiredDependenciesUsolveAll_generated;

function requireDependenciesUsolveAll_generated () {
	if (hasRequiredDependenciesUsolveAll_generated) return dependenciesUsolveAll_generated;
	hasRequiredDependenciesUsolveAll_generated = 1;

	Object.defineProperty(dependenciesUsolveAll_generated, "__esModule", {
	  value: true
	});
	dependenciesUsolveAll_generated.usolveAllDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUsolveAll_generated.usolveAllDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createUsolveAll: _factoriesAny.createUsolveAll
	};
	return dependenciesUsolveAll_generated;
}

var hasRequiredDependenciesEigs_generated;

function requireDependenciesEigs_generated () {
	if (hasRequiredDependenciesEigs_generated) return dependenciesEigs_generated;
	hasRequiredDependenciesEigs_generated = 1;

	Object.defineProperty(dependenciesEigs_generated, "__esModule", {
	  value: true
	});
	dependenciesEigs_generated.eigsDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesAtanGenerated = requireDependenciesAtan_generated();
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesColumnGenerated = requireDependenciesColumn_generated();
	var _dependenciesComplexGenerated = requireDependenciesComplex_generated();
	var _dependenciesCosGenerated = requireDependenciesCos_generated();
	var _dependenciesDiagGenerated = requireDependenciesDiag_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesDotGenerated = requireDependenciesDot_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
	var _dependenciesImGenerated = requireDependenciesIm_generated();
	var _dependenciesInvGenerated = requireDependenciesInv_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMatrixFromColumnsGenerated = requireDependenciesMatrixFromColumns_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesQrGenerated = requireDependenciesQr_generated();
	var _dependenciesReGenerated = requireDependenciesRe_generated();
	var _dependenciesReshapeGenerated = requireDependenciesReshape_generated();
	var _dependenciesSinGenerated = requireDependenciesSin_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUsolveGenerated = requireDependenciesUsolve_generated();
	var _dependenciesUsolveAllGenerated = requireDependenciesUsolveAll_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesEigs_generated.eigsDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  atanDependencies: _dependenciesAtanGenerated.atanDependencies,
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  columnDependencies: _dependenciesColumnGenerated.columnDependencies,
	  complexDependencies: _dependenciesComplexGenerated.complexDependencies,
	  cosDependencies: _dependenciesCosGenerated.cosDependencies,
	  diagDependencies: _dependenciesDiagGenerated.diagDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  dotDependencies: _dependenciesDotGenerated.dotDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  flattenDependencies: _dependenciesFlattenGenerated.flattenDependencies,
	  imDependencies: _dependenciesImGenerated.imDependencies,
	  invDependencies: _dependenciesInvGenerated.invDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  matrixFromColumnsDependencies: _dependenciesMatrixFromColumnsGenerated.matrixFromColumnsDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  qrDependencies: _dependenciesQrGenerated.qrDependencies,
	  reDependencies: _dependenciesReGenerated.reDependencies,
	  reshapeDependencies: _dependenciesReshapeGenerated.reshapeDependencies,
	  sinDependencies: _dependenciesSinGenerated.sinDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  usolveDependencies: _dependenciesUsolveGenerated.usolveDependencies,
	  usolveAllDependencies: _dependenciesUsolveAllGenerated.usolveAllDependencies,
	  createEigs: _factoriesAny.createEigs
	};
	return dependenciesEigs_generated;
}

var dependenciesElectricConstant_generated = {};

var hasRequiredDependenciesElectricConstant_generated;

function requireDependenciesElectricConstant_generated () {
	if (hasRequiredDependenciesElectricConstant_generated) return dependenciesElectricConstant_generated;
	hasRequiredDependenciesElectricConstant_generated = 1;

	Object.defineProperty(dependenciesElectricConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesElectricConstant_generated.electricConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesElectricConstant_generated.electricConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createElectricConstant: _factoriesAny.createElectricConstant
	};
	return dependenciesElectricConstant_generated;
}

var dependenciesElectronMass_generated = {};

var hasRequiredDependenciesElectronMass_generated;

function requireDependenciesElectronMass_generated () {
	if (hasRequiredDependenciesElectronMass_generated) return dependenciesElectronMass_generated;
	hasRequiredDependenciesElectronMass_generated = 1;

	Object.defineProperty(dependenciesElectronMass_generated, "__esModule", {
	  value: true
	});
	dependenciesElectronMass_generated.electronMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesElectronMass_generated.electronMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createElectronMass: _factoriesAny.createElectronMass
	};
	return dependenciesElectronMass_generated;
}

var dependenciesElementaryCharge_generated = {};

var hasRequiredDependenciesElementaryCharge_generated;

function requireDependenciesElementaryCharge_generated () {
	if (hasRequiredDependenciesElementaryCharge_generated) return dependenciesElementaryCharge_generated;
	hasRequiredDependenciesElementaryCharge_generated = 1;

	Object.defineProperty(dependenciesElementaryCharge_generated, "__esModule", {
	  value: true
	});
	dependenciesElementaryCharge_generated.elementaryChargeDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesElementaryCharge_generated.elementaryChargeDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createElementaryCharge: _factoriesAny.createElementaryCharge
	};
	return dependenciesElementaryCharge_generated;
}

var dependenciesEqualText_generated = {};

var hasRequiredDependenciesEqualText_generated;

function requireDependenciesEqualText_generated () {
	if (hasRequiredDependenciesEqualText_generated) return dependenciesEqualText_generated;
	hasRequiredDependenciesEqualText_generated = 1;

	Object.defineProperty(dependenciesEqualText_generated, "__esModule", {
	  value: true
	});
	dependenciesEqualText_generated.equalTextDependencies = void 0;
	var _dependenciesCompareTextGenerated = requireDependenciesCompareText_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesEqualText_generated.equalTextDependencies = {
	  compareTextDependencies: _dependenciesCompareTextGenerated.compareTextDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createEqualText: _factoriesAny.createEqualText
	};
	return dependenciesEqualText_generated;
}

var dependenciesErf_generated = {};

var hasRequiredDependenciesErf_generated;

function requireDependenciesErf_generated () {
	if (hasRequiredDependenciesErf_generated) return dependenciesErf_generated;
	hasRequiredDependenciesErf_generated = 1;

	Object.defineProperty(dependenciesErf_generated, "__esModule", {
	  value: true
	});
	dependenciesErf_generated.erfDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesErf_generated.erfDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createErf: _factoriesAny.createErf
	};
	return dependenciesErf_generated;
}

var dependenciesEvaluate_generated = {};

var hasRequiredDependenciesEvaluate_generated;

function requireDependenciesEvaluate_generated () {
	if (hasRequiredDependenciesEvaluate_generated) return dependenciesEvaluate_generated;
	hasRequiredDependenciesEvaluate_generated = 1;

	Object.defineProperty(dependenciesEvaluate_generated, "__esModule", {
	  value: true
	});
	dependenciesEvaluate_generated.evaluateDependencies = void 0;
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesEvaluate_generated.evaluateDependencies = {
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createEvaluate: _factoriesAny.createEvaluate
	};
	return dependenciesEvaluate_generated;
}

var dependenciesExp_generated = {};

var hasRequiredDependenciesExp_generated;

function requireDependenciesExp_generated () {
	if (hasRequiredDependenciesExp_generated) return dependenciesExp_generated;
	hasRequiredDependenciesExp_generated = 1;

	Object.defineProperty(dependenciesExp_generated, "__esModule", {
	  value: true
	});
	dependenciesExp_generated.expDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesExp_generated.expDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createExp: _factoriesAny.createExp
	};
	return dependenciesExp_generated;
}

var dependenciesExpm_generated = {};

var hasRequiredDependenciesExpm_generated;

function requireDependenciesExpm_generated () {
	if (hasRequiredDependenciesExpm_generated) return dependenciesExpm_generated;
	hasRequiredDependenciesExpm_generated = 1;

	Object.defineProperty(dependenciesExpm_generated, "__esModule", {
	  value: true
	});
	dependenciesExpm_generated.expmDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesInvGenerated = requireDependenciesInv_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesExpm_generated.expmDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  invDependencies: _dependenciesInvGenerated.invDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createExpm: _factoriesAny.createExpm
	};
	return dependenciesExpm_generated;
}

var dependenciesExpm1_generated = {};

var hasRequiredDependenciesExpm1_generated;

function requireDependenciesExpm1_generated () {
	if (hasRequiredDependenciesExpm1_generated) return dependenciesExpm1_generated;
	hasRequiredDependenciesExpm1_generated = 1;

	Object.defineProperty(dependenciesExpm1_generated, "__esModule", {
	  value: true
	});
	dependenciesExpm1_generated.expm1Dependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesExpm1_generated.expm1Dependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createExpm1: _factoriesAny.createExpm1
	};
	return dependenciesExpm1_generated;
}

var dependenciesFalse_generated = {};

var hasRequiredDependenciesFalse_generated;

function requireDependenciesFalse_generated () {
	if (hasRequiredDependenciesFalse_generated) return dependenciesFalse_generated;
	hasRequiredDependenciesFalse_generated = 1;

	Object.defineProperty(dependenciesFalse_generated, "__esModule", {
	  value: true
	});
	dependenciesFalse_generated.falseDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFalse_generated.falseDependencies = {
	  createFalse: _factoriesAny.createFalse
	};
	return dependenciesFalse_generated;
}

var dependenciesFaraday_generated = {};

var hasRequiredDependenciesFaraday_generated;

function requireDependenciesFaraday_generated () {
	if (hasRequiredDependenciesFaraday_generated) return dependenciesFaraday_generated;
	hasRequiredDependenciesFaraday_generated = 1;

	Object.defineProperty(dependenciesFaraday_generated, "__esModule", {
	  value: true
	});
	dependenciesFaraday_generated.faradayDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFaraday_generated.faradayDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createFaraday: _factoriesAny.createFaraday
	};
	return dependenciesFaraday_generated;
}

var dependenciesFermiCoupling_generated = {};

var hasRequiredDependenciesFermiCoupling_generated;

function requireDependenciesFermiCoupling_generated () {
	if (hasRequiredDependenciesFermiCoupling_generated) return dependenciesFermiCoupling_generated;
	hasRequiredDependenciesFermiCoupling_generated = 1;

	Object.defineProperty(dependenciesFermiCoupling_generated, "__esModule", {
	  value: true
	});
	dependenciesFermiCoupling_generated.fermiCouplingDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFermiCoupling_generated.fermiCouplingDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createFermiCoupling: _factoriesAny.createFermiCoupling
	};
	return dependenciesFermiCoupling_generated;
}

var dependenciesFft_generated = {};

var dependenciesI_generated = {};

var hasRequiredDependenciesI_generated;

function requireDependenciesI_generated () {
	if (hasRequiredDependenciesI_generated) return dependenciesI_generated;
	hasRequiredDependenciesI_generated = 1;

	Object.defineProperty(dependenciesI_generated, "__esModule", {
	  value: true
	});
	dependenciesI_generated.iDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesI_generated.iDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  createI: _factoriesAny.createI
	};
	return dependenciesI_generated;
}

var dependenciesLog2_generated = {};

var hasRequiredDependenciesLog2_generated;

function requireDependenciesLog2_generated () {
	if (hasRequiredDependenciesLog2_generated) return dependenciesLog2_generated;
	hasRequiredDependenciesLog2_generated = 1;

	Object.defineProperty(dependenciesLog2_generated, "__esModule", {
	  value: true
	});
	dependenciesLog2_generated.log2Dependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLog2_generated.log2Dependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLog2: _factoriesAny.createLog2
	};
	return dependenciesLog2_generated;
}

var dependenciesTau_generated = {};

var hasRequiredDependenciesTau_generated;

function requireDependenciesTau_generated () {
	if (hasRequiredDependenciesTau_generated) return dependenciesTau_generated;
	hasRequiredDependenciesTau_generated = 1;

	Object.defineProperty(dependenciesTau_generated, "__esModule", {
	  value: true
	});
	dependenciesTau_generated.tauDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTau_generated.tauDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createTau: _factoriesAny.createTau
	};
	return dependenciesTau_generated;
}

var hasRequiredDependenciesFft_generated;

function requireDependenciesFft_generated () {
	if (hasRequiredDependenciesFft_generated) return dependenciesFft_generated;
	hasRequiredDependenciesFft_generated = 1;

	Object.defineProperty(dependenciesFft_generated, "__esModule", {
	  value: true
	});
	dependenciesFft_generated.fftDependencies = void 0;
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesCeilGenerated = requireDependenciesCeil_generated();
	var _dependenciesConjGenerated = requireDependenciesConj_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesDotDivideGenerated = requireDependenciesDotDivide_generated();
	var _dependenciesExpGenerated = requireDependenciesExp_generated();
	var _dependenciesIGenerated = requireDependenciesI_generated();
	var _dependenciesLog2Generated = requireDependenciesLog2_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesTauGenerated = requireDependenciesTau_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFft_generated.fftDependencies = {
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  ceilDependencies: _dependenciesCeilGenerated.ceilDependencies,
	  conjDependencies: _dependenciesConjGenerated.conjDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  dotDivideDependencies: _dependenciesDotDivideGenerated.dotDivideDependencies,
	  expDependencies: _dependenciesExpGenerated.expDependencies,
	  iDependencies: _dependenciesIGenerated.iDependencies,
	  log2Dependencies: _dependenciesLog2Generated.log2Dependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  tauDependencies: _dependenciesTauGenerated.tauDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFft: _factoriesAny.createFft
	};
	return dependenciesFft_generated;
}

var dependenciesFibonacciHeapClass_generated = {};

var hasRequiredDependenciesFibonacciHeapClass_generated;

function requireDependenciesFibonacciHeapClass_generated () {
	if (hasRequiredDependenciesFibonacciHeapClass_generated) return dependenciesFibonacciHeapClass_generated;
	hasRequiredDependenciesFibonacciHeapClass_generated = 1;

	Object.defineProperty(dependenciesFibonacciHeapClass_generated, "__esModule", {
	  value: true
	});
	dependenciesFibonacciHeapClass_generated.FibonacciHeapDependencies = void 0;
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFibonacciHeapClass_generated.FibonacciHeapDependencies = {
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  createFibonacciHeapClass: _factoriesAny.createFibonacciHeapClass
	};
	return dependenciesFibonacciHeapClass_generated;
}

var dependenciesFilter_generated = {};

var hasRequiredDependenciesFilter_generated;

function requireDependenciesFilter_generated () {
	if (hasRequiredDependenciesFilter_generated) return dependenciesFilter_generated;
	hasRequiredDependenciesFilter_generated = 1;

	Object.defineProperty(dependenciesFilter_generated, "__esModule", {
	  value: true
	});
	dependenciesFilter_generated.filterDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFilter_generated.filterDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFilter: _factoriesAny.createFilter
	};
	return dependenciesFilter_generated;
}

var dependenciesFilterTransform_generated = {};

var hasRequiredDependenciesFilterTransform_generated;

function requireDependenciesFilterTransform_generated () {
	if (hasRequiredDependenciesFilterTransform_generated) return dependenciesFilterTransform_generated;
	hasRequiredDependenciesFilterTransform_generated = 1;

	Object.defineProperty(dependenciesFilterTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesFilterTransform_generated.filterTransformDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFilterTransform_generated.filterTransformDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFilterTransform: _factoriesAny.createFilterTransform
	};
	return dependenciesFilterTransform_generated;
}

var dependenciesFineStructure_generated = {};

var hasRequiredDependenciesFineStructure_generated;

function requireDependenciesFineStructure_generated () {
	if (hasRequiredDependenciesFineStructure_generated) return dependenciesFineStructure_generated;
	hasRequiredDependenciesFineStructure_generated = 1;

	Object.defineProperty(dependenciesFineStructure_generated, "__esModule", {
	  value: true
	});
	dependenciesFineStructure_generated.fineStructureDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFineStructure_generated.fineStructureDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createFineStructure: _factoriesAny.createFineStructure
	};
	return dependenciesFineStructure_generated;
}

var dependenciesFirstRadiation_generated = {};

var hasRequiredDependenciesFirstRadiation_generated;

function requireDependenciesFirstRadiation_generated () {
	if (hasRequiredDependenciesFirstRadiation_generated) return dependenciesFirstRadiation_generated;
	hasRequiredDependenciesFirstRadiation_generated = 1;

	Object.defineProperty(dependenciesFirstRadiation_generated, "__esModule", {
	  value: true
	});
	dependenciesFirstRadiation_generated.firstRadiationDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFirstRadiation_generated.firstRadiationDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createFirstRadiation: _factoriesAny.createFirstRadiation
	};
	return dependenciesFirstRadiation_generated;
}

var dependenciesForEach_generated = {};

var hasRequiredDependenciesForEach_generated;

function requireDependenciesForEach_generated () {
	if (hasRequiredDependenciesForEach_generated) return dependenciesForEach_generated;
	hasRequiredDependenciesForEach_generated = 1;

	Object.defineProperty(dependenciesForEach_generated, "__esModule", {
	  value: true
	});
	dependenciesForEach_generated.forEachDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesForEach_generated.forEachDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createForEach: _factoriesAny.createForEach
	};
	return dependenciesForEach_generated;
}

var dependenciesForEachTransform_generated = {};

var hasRequiredDependenciesForEachTransform_generated;

function requireDependenciesForEachTransform_generated () {
	if (hasRequiredDependenciesForEachTransform_generated) return dependenciesForEachTransform_generated;
	hasRequiredDependenciesForEachTransform_generated = 1;

	Object.defineProperty(dependenciesForEachTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesForEachTransform_generated.forEachTransformDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesForEachTransform_generated.forEachTransformDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createForEachTransform: _factoriesAny.createForEachTransform
	};
	return dependenciesForEachTransform_generated;
}

var dependenciesFreqz_generated = {};

var hasRequiredDependenciesFreqz_generated;

function requireDependenciesFreqz_generated () {
	if (hasRequiredDependenciesFreqz_generated) return dependenciesFreqz_generated;
	hasRequiredDependenciesFreqz_generated = 1;

	Object.defineProperty(dependenciesFreqz_generated, "__esModule", {
	  value: true
	});
	dependenciesFreqz_generated.freqzDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesFreqz_generated.freqzDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createFreqz: _factoriesAny.createFreqz
	};
	return dependenciesFreqz_generated;
}

var dependenciesGasConstant_generated = {};

var hasRequiredDependenciesGasConstant_generated;

function requireDependenciesGasConstant_generated () {
	if (hasRequiredDependenciesGasConstant_generated) return dependenciesGasConstant_generated;
	hasRequiredDependenciesGasConstant_generated = 1;

	Object.defineProperty(dependenciesGasConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesGasConstant_generated.gasConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesGasConstant_generated.gasConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createGasConstant: _factoriesAny.createGasConstant
	};
	return dependenciesGasConstant_generated;
}

var dependenciesGcd_generated = {};

var hasRequiredDependenciesGcd_generated;

function requireDependenciesGcd_generated () {
	if (hasRequiredDependenciesGcd_generated) return dependenciesGcd_generated;
	hasRequiredDependenciesGcd_generated = 1;

	Object.defineProperty(dependenciesGcd_generated, "__esModule", {
	  value: true
	});
	dependenciesGcd_generated.gcdDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRoundGenerated = requireDependenciesRound_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesGcd_generated.gcdDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  roundDependencies: _dependenciesRoundGenerated.roundDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createGcd: _factoriesAny.createGcd
	};
	return dependenciesGcd_generated;
}

var dependenciesGravitationConstant_generated = {};

var hasRequiredDependenciesGravitationConstant_generated;

function requireDependenciesGravitationConstant_generated () {
	if (hasRequiredDependenciesGravitationConstant_generated) return dependenciesGravitationConstant_generated;
	hasRequiredDependenciesGravitationConstant_generated = 1;

	Object.defineProperty(dependenciesGravitationConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesGravitationConstant_generated.gravitationConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesGravitationConstant_generated.gravitationConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createGravitationConstant: _factoriesAny.createGravitationConstant
	};
	return dependenciesGravitationConstant_generated;
}

var dependenciesGravity_generated = {};

var hasRequiredDependenciesGravity_generated;

function requireDependenciesGravity_generated () {
	if (hasRequiredDependenciesGravity_generated) return dependenciesGravity_generated;
	hasRequiredDependenciesGravity_generated = 1;

	Object.defineProperty(dependenciesGravity_generated, "__esModule", {
	  value: true
	});
	dependenciesGravity_generated.gravityDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesGravity_generated.gravityDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createGravity: _factoriesAny.createGravity
	};
	return dependenciesGravity_generated;
}

var dependenciesHartreeEnergy_generated = {};

var hasRequiredDependenciesHartreeEnergy_generated;

function requireDependenciesHartreeEnergy_generated () {
	if (hasRequiredDependenciesHartreeEnergy_generated) return dependenciesHartreeEnergy_generated;
	hasRequiredDependenciesHartreeEnergy_generated = 1;

	Object.defineProperty(dependenciesHartreeEnergy_generated, "__esModule", {
	  value: true
	});
	dependenciesHartreeEnergy_generated.hartreeEnergyDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesHartreeEnergy_generated.hartreeEnergyDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createHartreeEnergy: _factoriesAny.createHartreeEnergy
	};
	return dependenciesHartreeEnergy_generated;
}

var dependenciesHasNumericValue_generated = {};

var hasRequiredDependenciesHasNumericValue_generated;

function requireDependenciesHasNumericValue_generated () {
	if (hasRequiredDependenciesHasNumericValue_generated) return dependenciesHasNumericValue_generated;
	hasRequiredDependenciesHasNumericValue_generated = 1;

	Object.defineProperty(dependenciesHasNumericValue_generated, "__esModule", {
	  value: true
	});
	dependenciesHasNumericValue_generated.hasNumericValueDependencies = void 0;
	var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesHasNumericValue_generated.hasNumericValueDependencies = {
	  isNumericDependencies: _dependenciesIsNumericGenerated.isNumericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createHasNumericValue: _factoriesAny.createHasNumericValue
	};
	return dependenciesHasNumericValue_generated;
}

var dependenciesHelp_generated = {};

var dependenciesHelpClass_generated = {};

var hasRequiredDependenciesHelpClass_generated;

function requireDependenciesHelpClass_generated () {
	if (hasRequiredDependenciesHelpClass_generated) return dependenciesHelpClass_generated;
	hasRequiredDependenciesHelpClass_generated = 1;

	Object.defineProperty(dependenciesHelpClass_generated, "__esModule", {
	  value: true
	});
	dependenciesHelpClass_generated.HelpDependencies = void 0;
	var _dependenciesEvaluateGenerated = requireDependenciesEvaluate_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesHelpClass_generated.HelpDependencies = {
	  evaluateDependencies: _dependenciesEvaluateGenerated.evaluateDependencies,
	  createHelpClass: _factoriesAny.createHelpClass
	};
	return dependenciesHelpClass_generated;
}

var hasRequiredDependenciesHelp_generated;

function requireDependenciesHelp_generated () {
	if (hasRequiredDependenciesHelp_generated) return dependenciesHelp_generated;
	hasRequiredDependenciesHelp_generated = 1;

	Object.defineProperty(dependenciesHelp_generated, "__esModule", {
	  value: true
	});
	dependenciesHelp_generated.helpDependencies = void 0;
	var _dependenciesHelpClassGenerated = requireDependenciesHelpClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesHelp_generated.helpDependencies = {
	  HelpDependencies: _dependenciesHelpClassGenerated.HelpDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createHelp: _factoriesAny.createHelp
	};
	return dependenciesHelp_generated;
}

var dependenciesHex_generated = {};

var hasRequiredDependenciesHex_generated;

function requireDependenciesHex_generated () {
	if (hasRequiredDependenciesHex_generated) return dependenciesHex_generated;
	hasRequiredDependenciesHex_generated = 1;

	Object.defineProperty(dependenciesHex_generated, "__esModule", {
	  value: true
	});
	dependenciesHex_generated.hexDependencies = void 0;
	var _dependenciesFormatGenerated = requireDependenciesFormat_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesHex_generated.hexDependencies = {
	  formatDependencies: _dependenciesFormatGenerated.formatDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createHex: _factoriesAny.createHex
	};
	return dependenciesHex_generated;
}

var dependenciesHypot_generated = {};

var hasRequiredDependenciesHypot_generated;

function requireDependenciesHypot_generated () {
	if (hasRequiredDependenciesHypot_generated) return dependenciesHypot_generated;
	hasRequiredDependenciesHypot_generated = 1;

	Object.defineProperty(dependenciesHypot_generated, "__esModule", {
	  value: true
	});
	dependenciesHypot_generated.hypotDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesHypot_generated.hypotDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  isPositiveDependencies: _dependenciesIsPositiveGenerated.isPositiveDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createHypot: _factoriesAny.createHypot
	};
	return dependenciesHypot_generated;
}

var dependenciesIfft_generated = {};

var hasRequiredDependenciesIfft_generated;

function requireDependenciesIfft_generated () {
	if (hasRequiredDependenciesIfft_generated) return dependenciesIfft_generated;
	hasRequiredDependenciesIfft_generated = 1;

	Object.defineProperty(dependenciesIfft_generated, "__esModule", {
	  value: true
	});
	dependenciesIfft_generated.ifftDependencies = void 0;
	var _dependenciesConjGenerated = requireDependenciesConj_generated();
	var _dependenciesDotDivideGenerated = requireDependenciesDotDivide_generated();
	var _dependenciesFftGenerated = requireDependenciesFft_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIfft_generated.ifftDependencies = {
	  conjDependencies: _dependenciesConjGenerated.conjDependencies,
	  dotDivideDependencies: _dependenciesDotDivideGenerated.dotDivideDependencies,
	  fftDependencies: _dependenciesFftGenerated.fftDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIfft: _factoriesAny.createIfft
	};
	return dependenciesIfft_generated;
}

var dependenciesIndex_generated = {};

var hasRequiredDependenciesIndex_generated;

function requireDependenciesIndex_generated () {
	if (hasRequiredDependenciesIndex_generated) return dependenciesIndex_generated;
	hasRequiredDependenciesIndex_generated = 1;

	Object.defineProperty(dependenciesIndex_generated, "__esModule", {
	  value: true
	});
	dependenciesIndex_generated.indexDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIndex_generated.indexDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIndex: _factoriesAny.createIndex
	};
	return dependenciesIndex_generated;
}

var dependenciesIndexTransform_generated = {};

var hasRequiredDependenciesIndexTransform_generated;

function requireDependenciesIndexTransform_generated () {
	if (hasRequiredDependenciesIndexTransform_generated) return dependenciesIndexTransform_generated;
	hasRequiredDependenciesIndexTransform_generated = 1;

	Object.defineProperty(dependenciesIndexTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesIndexTransform_generated.indexTransformDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesGetMatrixDataTypeGenerated = requireDependenciesGetMatrixDataType_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIndexTransform_generated.indexTransformDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  getMatrixDataTypeDependencies: _dependenciesGetMatrixDataTypeGenerated.getMatrixDataTypeDependencies,
	  createIndexTransform: _factoriesAny.createIndexTransform
	};
	return dependenciesIndexTransform_generated;
}

var dependenciesInfinity_generated = {};

var hasRequiredDependenciesInfinity_generated;

function requireDependenciesInfinity_generated () {
	if (hasRequiredDependenciesInfinity_generated) return dependenciesInfinity_generated;
	hasRequiredDependenciesInfinity_generated = 1;

	Object.defineProperty(dependenciesInfinity_generated, "__esModule", {
	  value: true
	});
	dependenciesInfinity_generated.InfinityDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesInfinity_generated.InfinityDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createInfinity: _factoriesAny.createInfinity
	};
	return dependenciesInfinity_generated;
}

var dependenciesIntersect_generated = {};

var hasRequiredDependenciesIntersect_generated;

function requireDependenciesIntersect_generated () {
	if (hasRequiredDependenciesIntersect_generated) return dependenciesIntersect_generated;
	hasRequiredDependenciesIntersect_generated = 1;

	Object.defineProperty(dependenciesIntersect_generated, "__esModule", {
	  value: true
	});
	dependenciesIntersect_generated.intersectDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
	var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIntersect_generated.intersectDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  flattenDependencies: _dependenciesFlattenGenerated.flattenDependencies,
	  isNumericDependencies: _dependenciesIsNumericGenerated.isNumericDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIntersect: _factoriesAny.createIntersect
	};
	return dependenciesIntersect_generated;
}

var dependenciesInverseConductanceQuantum_generated = {};

var hasRequiredDependenciesInverseConductanceQuantum_generated;

function requireDependenciesInverseConductanceQuantum_generated () {
	if (hasRequiredDependenciesInverseConductanceQuantum_generated) return dependenciesInverseConductanceQuantum_generated;
	hasRequiredDependenciesInverseConductanceQuantum_generated = 1;

	Object.defineProperty(dependenciesInverseConductanceQuantum_generated, "__esModule", {
	  value: true
	});
	dependenciesInverseConductanceQuantum_generated.inverseConductanceQuantumDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesInverseConductanceQuantum_generated.inverseConductanceQuantumDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createInverseConductanceQuantum: _factoriesAny.createInverseConductanceQuantum
	};
	return dependenciesInverseConductanceQuantum_generated;
}

var dependenciesInvmod_generated = {};

var dependenciesMod_generated = {};

var hasRequiredDependenciesMod_generated;

function requireDependenciesMod_generated () {
	if (hasRequiredDependenciesMod_generated) return dependenciesMod_generated;
	hasRequiredDependenciesMod_generated = 1;

	Object.defineProperty(dependenciesMod_generated, "__esModule", {
	  value: true
	});
	dependenciesMod_generated.modDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRoundGenerated = requireDependenciesRound_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMod_generated.modDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  roundDependencies: _dependenciesRoundGenerated.roundDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createMod: _factoriesAny.createMod
	};
	return dependenciesMod_generated;
}

var dependenciesXgcd_generated = {};

var hasRequiredDependenciesXgcd_generated;

function requireDependenciesXgcd_generated () {
	if (hasRequiredDependenciesXgcd_generated) return dependenciesXgcd_generated;
	hasRequiredDependenciesXgcd_generated = 1;

	Object.defineProperty(dependenciesXgcd_generated, "__esModule", {
	  value: true
	});
	dependenciesXgcd_generated.xgcdDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesXgcd_generated.xgcdDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createXgcd: _factoriesAny.createXgcd
	};
	return dependenciesXgcd_generated;
}

var hasRequiredDependenciesInvmod_generated;

function requireDependenciesInvmod_generated () {
	if (hasRequiredDependenciesInvmod_generated) return dependenciesInvmod_generated;
	hasRequiredDependenciesInvmod_generated = 1;

	Object.defineProperty(dependenciesInvmod_generated, "__esModule", {
	  value: true
	});
	dependenciesInvmod_generated.invmodDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesModGenerated = requireDependenciesMod_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesXgcdGenerated = requireDependenciesXgcd_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesInvmod_generated.invmodDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  modDependencies: _dependenciesModGenerated.modDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  xgcdDependencies: _dependenciesXgcdGenerated.xgcdDependencies,
	  createInvmod: _factoriesAny.createInvmod
	};
	return dependenciesInvmod_generated;
}

var dependenciesIsFinite_generated = {};

var dependenciesMap_generated = {};

var hasRequiredDependenciesMap_generated;

function requireDependenciesMap_generated () {
	if (hasRequiredDependenciesMap_generated) return dependenciesMap_generated;
	hasRequiredDependenciesMap_generated = 1;

	Object.defineProperty(dependenciesMap_generated, "__esModule", {
	  value: true
	});
	dependenciesMap_generated.mapDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMap_generated.mapDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMap: _factoriesAny.createMap
	};
	return dependenciesMap_generated;
}

var hasRequiredDependenciesIsFinite_generated;

function requireDependenciesIsFinite_generated () {
	if (hasRequiredDependenciesIsFinite_generated) return dependenciesIsFinite_generated;
	hasRequiredDependenciesIsFinite_generated = 1;

	Object.defineProperty(dependenciesIsFinite_generated, "__esModule", {
	  value: true
	});
	dependenciesIsFinite_generated.isFiniteDependencies = void 0;
	var _dependenciesIsBoundedGenerated = requireDependenciesIsBounded_generated();
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsFinite_generated.isFiniteDependencies = {
	  isBoundedDependencies: _dependenciesIsBoundedGenerated.isBoundedDependencies,
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsFinite: _factoriesAny.createIsFinite
	};
	return dependenciesIsFinite_generated;
}

var dependenciesIsNaN_generated = {};

var hasRequiredDependenciesIsNaN_generated;

function requireDependenciesIsNaN_generated () {
	if (hasRequiredDependenciesIsNaN_generated) return dependenciesIsNaN_generated;
	hasRequiredDependenciesIsNaN_generated = 1;

	Object.defineProperty(dependenciesIsNaN_generated, "__esModule", {
	  value: true
	});
	dependenciesIsNaN_generated.isNaNDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsNaN_generated.isNaNDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsNaN: _factoriesAny.createIsNaN
	};
	return dependenciesIsNaN_generated;
}

var dependenciesIsPrime_generated = {};

var hasRequiredDependenciesIsPrime_generated;

function requireDependenciesIsPrime_generated () {
	if (hasRequiredDependenciesIsPrime_generated) return dependenciesIsPrime_generated;
	hasRequiredDependenciesIsPrime_generated = 1;

	Object.defineProperty(dependenciesIsPrime_generated, "__esModule", {
	  value: true
	});
	dependenciesIsPrime_generated.isPrimeDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesIsPrime_generated.isPrimeDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createIsPrime: _factoriesAny.createIsPrime
	};
	return dependenciesIsPrime_generated;
}

var dependenciesKldivergence_generated = {};

var dependenciesLog_generated = {};

var dependenciesTypeOf_generated = {};

var hasRequiredDependenciesTypeOf_generated;

function requireDependenciesTypeOf_generated () {
	if (hasRequiredDependenciesTypeOf_generated) return dependenciesTypeOf_generated;
	hasRequiredDependenciesTypeOf_generated = 1;

	Object.defineProperty(dependenciesTypeOf_generated, "__esModule", {
	  value: true
	});
	dependenciesTypeOf_generated.typeOfDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTypeOf_generated.typeOfDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createTypeOf: _factoriesAny.createTypeOf
	};
	return dependenciesTypeOf_generated;
}

var hasRequiredDependenciesLog_generated;

function requireDependenciesLog_generated () {
	if (hasRequiredDependenciesLog_generated) return dependenciesLog_generated;
	hasRequiredDependenciesLog_generated = 1;

	Object.defineProperty(dependenciesLog_generated, "__esModule", {
	  value: true
	});
	dependenciesLog_generated.logDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesTypeOfGenerated = requireDependenciesTypeOf_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLog_generated.logDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  typeOfDependencies: _dependenciesTypeOfGenerated.typeOfDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLog: _factoriesAny.createLog
	};
	return dependenciesLog_generated;
}

var hasRequiredDependenciesKldivergence_generated;

function requireDependenciesKldivergence_generated () {
	if (hasRequiredDependenciesKldivergence_generated) return dependenciesKldivergence_generated;
	hasRequiredDependenciesKldivergence_generated = 1;

	Object.defineProperty(dependenciesKldivergence_generated, "__esModule", {
	  value: true
	});
	dependenciesKldivergence_generated.kldivergenceDependencies = void 0;
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesDotDivideGenerated = requireDependenciesDotDivide_generated();
	var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
	var _dependenciesLogGenerated = requireDependenciesLog_generated();
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSumGenerated = requireDependenciesSum_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesKldivergence_generated.kldivergenceDependencies = {
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  dotDivideDependencies: _dependenciesDotDivideGenerated.dotDivideDependencies,
	  isNumericDependencies: _dependenciesIsNumericGenerated.isNumericDependencies,
	  logDependencies: _dependenciesLogGenerated.logDependencies,
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  sumDependencies: _dependenciesSumGenerated.sumDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createKldivergence: _factoriesAny.createKldivergence
	};
	return dependenciesKldivergence_generated;
}

var dependenciesKlitzing_generated = {};

var hasRequiredDependenciesKlitzing_generated;

function requireDependenciesKlitzing_generated () {
	if (hasRequiredDependenciesKlitzing_generated) return dependenciesKlitzing_generated;
	hasRequiredDependenciesKlitzing_generated = 1;

	Object.defineProperty(dependenciesKlitzing_generated, "__esModule", {
	  value: true
	});
	dependenciesKlitzing_generated.klitzingDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesKlitzing_generated.klitzingDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createKlitzing: _factoriesAny.createKlitzing
	};
	return dependenciesKlitzing_generated;
}

var dependenciesKron_generated = {};

var hasRequiredDependenciesKron_generated;

function requireDependenciesKron_generated () {
	if (hasRequiredDependenciesKron_generated) return dependenciesKron_generated;
	hasRequiredDependenciesKron_generated = 1;

	Object.defineProperty(dependenciesKron_generated, "__esModule", {
	  value: true
	});
	dependenciesKron_generated.kronDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesKron_generated.kronDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createKron: _factoriesAny.createKron
	};
	return dependenciesKron_generated;
}

var dependenciesLN10_generated = {};

var hasRequiredDependenciesLN10_generated;

function requireDependenciesLN10_generated () {
	if (hasRequiredDependenciesLN10_generated) return dependenciesLN10_generated;
	hasRequiredDependenciesLN10_generated = 1;

	Object.defineProperty(dependenciesLN10_generated, "__esModule", {
	  value: true
	});
	dependenciesLN10_generated.LN10Dependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLN10_generated.LN10Dependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createLN10: _factoriesAny.createLN10
	};
	return dependenciesLN10_generated;
}

var dependenciesLN2_generated = {};

var hasRequiredDependenciesLN2_generated;

function requireDependenciesLN2_generated () {
	if (hasRequiredDependenciesLN2_generated) return dependenciesLN2_generated;
	hasRequiredDependenciesLN2_generated = 1;

	Object.defineProperty(dependenciesLN2_generated, "__esModule", {
	  value: true
	});
	dependenciesLN2_generated.LN2Dependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLN2_generated.LN2Dependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createLN2: _factoriesAny.createLN2
	};
	return dependenciesLN2_generated;
}

var dependenciesLOG10E_generated = {};

var hasRequiredDependenciesLOG10E_generated;

function requireDependenciesLOG10E_generated () {
	if (hasRequiredDependenciesLOG10E_generated) return dependenciesLOG10E_generated;
	hasRequiredDependenciesLOG10E_generated = 1;

	Object.defineProperty(dependenciesLOG10E_generated, "__esModule", {
	  value: true
	});
	dependenciesLOG10E_generated.LOG10EDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLOG10E_generated.LOG10EDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createLOG10E: _factoriesAny.createLOG10E
	};
	return dependenciesLOG10E_generated;
}

var dependenciesLOG2E_generated = {};

var hasRequiredDependenciesLOG2E_generated;

function requireDependenciesLOG2E_generated () {
	if (hasRequiredDependenciesLOG2E_generated) return dependenciesLOG2E_generated;
	hasRequiredDependenciesLOG2E_generated = 1;

	Object.defineProperty(dependenciesLOG2E_generated, "__esModule", {
	  value: true
	});
	dependenciesLOG2E_generated.LOG2EDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLOG2E_generated.LOG2EDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createLOG2E: _factoriesAny.createLOG2E
	};
	return dependenciesLOG2E_generated;
}

var dependenciesLcm_generated = {};

var hasRequiredDependenciesLcm_generated;

function requireDependenciesLcm_generated () {
	if (hasRequiredDependenciesLcm_generated) return dependenciesLcm_generated;
	hasRequiredDependenciesLcm_generated = 1;

	Object.defineProperty(dependenciesLcm_generated, "__esModule", {
	  value: true
	});
	dependenciesLcm_generated.lcmDependencies = void 0;
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLcm_generated.lcmDependencies = {
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLcm: _factoriesAny.createLcm
	};
	return dependenciesLcm_generated;
}

var dependenciesLeafCount_generated = {};

var hasRequiredDependenciesLeafCount_generated;

function requireDependenciesLeafCount_generated () {
	if (hasRequiredDependenciesLeafCount_generated) return dependenciesLeafCount_generated;
	hasRequiredDependenciesLeafCount_generated = 1;

	Object.defineProperty(dependenciesLeafCount_generated, "__esModule", {
	  value: true
	});
	dependenciesLeafCount_generated.leafCountDependencies = void 0;
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLeafCount_generated.leafCountDependencies = {
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLeafCount: _factoriesAny.createLeafCount
	};
	return dependenciesLeafCount_generated;
}

var dependenciesLeftShift_generated = {};

var hasRequiredDependenciesLeftShift_generated;

function requireDependenciesLeftShift_generated () {
	if (hasRequiredDependenciesLeftShift_generated) return dependenciesLeftShift_generated;
	hasRequiredDependenciesLeftShift_generated = 1;

	Object.defineProperty(dependenciesLeftShift_generated, "__esModule", {
	  value: true
	});
	dependenciesLeftShift_generated.leftShiftDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLeftShift_generated.leftShiftDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createLeftShift: _factoriesAny.createLeftShift
	};
	return dependenciesLeftShift_generated;
}

var dependenciesLgamma_generated = {};

var hasRequiredDependenciesLgamma_generated;

function requireDependenciesLgamma_generated () {
	if (hasRequiredDependenciesLgamma_generated) return dependenciesLgamma_generated;
	hasRequiredDependenciesLgamma_generated = 1;

	Object.defineProperty(dependenciesLgamma_generated, "__esModule", {
	  value: true
	});
	dependenciesLgamma_generated.lgammaDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLgamma_generated.lgammaDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLgamma: _factoriesAny.createLgamma
	};
	return dependenciesLgamma_generated;
}

var dependenciesLog10_generated = {};

var hasRequiredDependenciesLog10_generated;

function requireDependenciesLog10_generated () {
	if (hasRequiredDependenciesLog10_generated) return dependenciesLog10_generated;
	hasRequiredDependenciesLog10_generated = 1;

	Object.defineProperty(dependenciesLog10_generated, "__esModule", {
	  value: true
	});
	dependenciesLog10_generated.log10Dependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLog10_generated.log10Dependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLog10: _factoriesAny.createLog10
	};
	return dependenciesLog10_generated;
}

var dependenciesLog1p_generated = {};

var hasRequiredDependenciesLog1p_generated;

function requireDependenciesLog1p_generated () {
	if (hasRequiredDependenciesLog1p_generated) return dependenciesLog1p_generated;
	hasRequiredDependenciesLog1p_generated = 1;

	Object.defineProperty(dependenciesLog1p_generated, "__esModule", {
	  value: true
	});
	dependenciesLog1p_generated.log1pDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesLogGenerated = requireDependenciesLog_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLog1p_generated.log1pDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  logDependencies: _dependenciesLogGenerated.logDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLog1p: _factoriesAny.createLog1p
	};
	return dependenciesLog1p_generated;
}

var dependenciesLoschmidt_generated = {};

var hasRequiredDependenciesLoschmidt_generated;

function requireDependenciesLoschmidt_generated () {
	if (hasRequiredDependenciesLoschmidt_generated) return dependenciesLoschmidt_generated;
	hasRequiredDependenciesLoschmidt_generated = 1;

	Object.defineProperty(dependenciesLoschmidt_generated, "__esModule", {
	  value: true
	});
	dependenciesLoschmidt_generated.loschmidtDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLoschmidt_generated.loschmidtDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createLoschmidt: _factoriesAny.createLoschmidt
	};
	return dependenciesLoschmidt_generated;
}

var dependenciesLsolve_generated = {};

var hasRequiredDependenciesLsolve_generated;

function requireDependenciesLsolve_generated () {
	if (hasRequiredDependenciesLsolve_generated) return dependenciesLsolve_generated;
	hasRequiredDependenciesLsolve_generated = 1;

	Object.defineProperty(dependenciesLsolve_generated, "__esModule", {
	  value: true
	});
	dependenciesLsolve_generated.lsolveDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLsolve_generated.lsolveDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLsolve: _factoriesAny.createLsolve
	};
	return dependenciesLsolve_generated;
}

var dependenciesLsolveAll_generated = {};

var hasRequiredDependenciesLsolveAll_generated;

function requireDependenciesLsolveAll_generated () {
	if (hasRequiredDependenciesLsolveAll_generated) return dependenciesLsolveAll_generated;
	hasRequiredDependenciesLsolveAll_generated = 1;

	Object.defineProperty(dependenciesLsolveAll_generated, "__esModule", {
	  value: true
	});
	dependenciesLsolveAll_generated.lsolveAllDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLsolveAll_generated.lsolveAllDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLsolveAll: _factoriesAny.createLsolveAll
	};
	return dependenciesLsolveAll_generated;
}

var dependenciesLup_generated = {};

var dependenciesSpaClass_generated = {};

var hasRequiredDependenciesSpaClass_generated;

function requireDependenciesSpaClass_generated () {
	if (hasRequiredDependenciesSpaClass_generated) return dependenciesSpaClass_generated;
	hasRequiredDependenciesSpaClass_generated = 1;

	Object.defineProperty(dependenciesSpaClass_generated, "__esModule", {
	  value: true
	});
	dependenciesSpaClass_generated.SpaDependencies = void 0;
	var _dependenciesFibonacciHeapClassGenerated = requireDependenciesFibonacciHeapClass_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSpaClass_generated.SpaDependencies = {
	  FibonacciHeapDependencies: _dependenciesFibonacciHeapClassGenerated.FibonacciHeapDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  createSpaClass: _factoriesAny.createSpaClass
	};
	return dependenciesSpaClass_generated;
}

var hasRequiredDependenciesLup_generated;

function requireDependenciesLup_generated () {
	if (hasRequiredDependenciesLup_generated) return dependenciesLup_generated;
	hasRequiredDependenciesLup_generated = 1;

	Object.defineProperty(dependenciesLup_generated, "__esModule", {
	  value: true
	});
	dependenciesLup_generated.lupDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSpaClassGenerated = requireDependenciesSpaClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLup_generated.lupDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SpaDependencies: _dependenciesSpaClassGenerated.SpaDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  subtractScalarDependencies: _dependenciesSubtractScalarGenerated.subtractScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createLup: _factoriesAny.createLup
	};
	return dependenciesLup_generated;
}

var dependenciesLusolve_generated = {};

var dependenciesSlu_generated = {};

var hasRequiredDependenciesSlu_generated;

function requireDependenciesSlu_generated () {
	if (hasRequiredDependenciesSlu_generated) return dependenciesSlu_generated;
	hasRequiredDependenciesSlu_generated = 1;

	Object.defineProperty(dependenciesSlu_generated, "__esModule", {
	  value: true
	});
	dependenciesSlu_generated.sluDependencies = void 0;
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesLargerEqGenerated = requireDependenciesLargerEq_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTransposeGenerated = requireDependenciesTranspose_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSlu_generated.sluDependencies = {
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  largerEqDependencies: _dependenciesLargerEqGenerated.largerEqDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  transposeDependencies: _dependenciesTransposeGenerated.transposeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSlu: _factoriesAny.createSlu
	};
	return dependenciesSlu_generated;
}

var hasRequiredDependenciesLusolve_generated;

function requireDependenciesLusolve_generated () {
	if (hasRequiredDependenciesLusolve_generated) return dependenciesLusolve_generated;
	hasRequiredDependenciesLusolve_generated = 1;

	Object.defineProperty(dependenciesLusolve_generated, "__esModule", {
	  value: true
	});
	dependenciesLusolve_generated.lusolveDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesLsolveGenerated = requireDependenciesLsolve_generated();
	var _dependenciesLupGenerated = requireDependenciesLup_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesSluGenerated = requireDependenciesSlu_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUsolveGenerated = requireDependenciesUsolve_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLusolve_generated.lusolveDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  lsolveDependencies: _dependenciesLsolveGenerated.lsolveDependencies,
	  lupDependencies: _dependenciesLupGenerated.lupDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  sluDependencies: _dependenciesSluGenerated.sluDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  usolveDependencies: _dependenciesUsolveGenerated.usolveDependencies,
	  createLusolve: _factoriesAny.createLusolve
	};
	return dependenciesLusolve_generated;
}

var dependenciesLyap_generated = {};

var dependenciesSylvester_generated = {};

var dependenciesSchur_generated = {};

var dependenciesNorm_generated = {};

var hasRequiredDependenciesNorm_generated;

function requireDependenciesNorm_generated () {
	if (hasRequiredDependenciesNorm_generated) return dependenciesNorm_generated;
	hasRequiredDependenciesNorm_generated = 1;

	Object.defineProperty(dependenciesNorm_generated, "__esModule", {
	  value: true
	});
	dependenciesNorm_generated.normDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesConjGenerated = requireDependenciesConj_generated();
	var _dependenciesCtransposeGenerated = requireDependenciesCtranspose_generated();
	var _dependenciesEigsGenerated = requireDependenciesEigs_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNorm_generated.normDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  conjDependencies: _dependenciesConjGenerated.conjDependencies,
	  ctransposeDependencies: _dependenciesCtransposeGenerated.ctransposeDependencies,
	  eigsDependencies: _dependenciesEigsGenerated.eigsDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNorm: _factoriesAny.createNorm
	};
	return dependenciesNorm_generated;
}

var hasRequiredDependenciesSchur_generated;

function requireDependenciesSchur_generated () {
	if (hasRequiredDependenciesSchur_generated) return dependenciesSchur_generated;
	hasRequiredDependenciesSchur_generated = 1;

	Object.defineProperty(dependenciesSchur_generated, "__esModule", {
	  value: true
	});
	dependenciesSchur_generated.schurDependencies = void 0;
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesNormGenerated = requireDependenciesNorm_generated();
	var _dependenciesQrGenerated = requireDependenciesQr_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSchur_generated.schurDependencies = {
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  normDependencies: _dependenciesNormGenerated.normDependencies,
	  qrDependencies: _dependenciesQrGenerated.qrDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSchur: _factoriesAny.createSchur
	};
	return dependenciesSchur_generated;
}

var hasRequiredDependenciesSylvester_generated;

function requireDependenciesSylvester_generated () {
	if (hasRequiredDependenciesSylvester_generated) return dependenciesSylvester_generated;
	hasRequiredDependenciesSylvester_generated = 1;

	Object.defineProperty(dependenciesSylvester_generated, "__esModule", {
	  value: true
	});
	dependenciesSylvester_generated.sylvesterDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesIndexGenerated = requireDependenciesIndex_generated();
	var _dependenciesLusolveGenerated = requireDependenciesLusolve_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMatrixFromColumnsGenerated = requireDependenciesMatrixFromColumns_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesRangeGenerated = requireDependenciesRange_generated();
	var _dependenciesSchurGenerated = requireDependenciesSchur_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTransposeGenerated = requireDependenciesTranspose_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSylvester_generated.sylvesterDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  indexDependencies: _dependenciesIndexGenerated.indexDependencies,
	  lusolveDependencies: _dependenciesLusolveGenerated.lusolveDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  matrixFromColumnsDependencies: _dependenciesMatrixFromColumnsGenerated.matrixFromColumnsDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  rangeDependencies: _dependenciesRangeGenerated.rangeDependencies,
	  schurDependencies: _dependenciesSchurGenerated.schurDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  transposeDependencies: _dependenciesTransposeGenerated.transposeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSylvester: _factoriesAny.createSylvester
	};
	return dependenciesSylvester_generated;
}

var hasRequiredDependenciesLyap_generated;

function requireDependenciesLyap_generated () {
	if (hasRequiredDependenciesLyap_generated) return dependenciesLyap_generated;
	hasRequiredDependenciesLyap_generated = 1;

	Object.defineProperty(dependenciesLyap_generated, "__esModule", {
	  value: true
	});
	dependenciesLyap_generated.lyapDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSylvesterGenerated = requireDependenciesSylvester_generated();
	var _dependenciesTransposeGenerated = requireDependenciesTranspose_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesLyap_generated.lyapDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  sylvesterDependencies: _dependenciesSylvesterGenerated.sylvesterDependencies,
	  transposeDependencies: _dependenciesTransposeGenerated.transposeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createLyap: _factoriesAny.createLyap
	};
	return dependenciesLyap_generated;
}

var dependenciesMad_generated = {};

var dependenciesMedian_generated = {};

var dependenciesPartitionSelect_generated = {};

var hasRequiredDependenciesPartitionSelect_generated;

function requireDependenciesPartitionSelect_generated () {
	if (hasRequiredDependenciesPartitionSelect_generated) return dependenciesPartitionSelect_generated;
	hasRequiredDependenciesPartitionSelect_generated = 1;

	Object.defineProperty(dependenciesPartitionSelect_generated, "__esModule", {
	  value: true
	});
	dependenciesPartitionSelect_generated.partitionSelectDependencies = void 0;
	var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPartitionSelect_generated.partitionSelectDependencies = {
	  compareDependencies: _dependenciesCompareGenerated.compareDependencies,
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  isNumericDependencies: _dependenciesIsNumericGenerated.isNumericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createPartitionSelect: _factoriesAny.createPartitionSelect
	};
	return dependenciesPartitionSelect_generated;
}

var hasRequiredDependenciesMedian_generated;

function requireDependenciesMedian_generated () {
	if (hasRequiredDependenciesMedian_generated) return dependenciesMedian_generated;
	hasRequiredDependenciesMedian_generated = 1;

	Object.defineProperty(dependenciesMedian_generated, "__esModule", {
	  value: true
	});
	dependenciesMedian_generated.medianDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesPartitionSelectGenerated = requireDependenciesPartitionSelect_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMedian_generated.medianDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  compareDependencies: _dependenciesCompareGenerated.compareDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  partitionSelectDependencies: _dependenciesPartitionSelectGenerated.partitionSelectDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMedian: _factoriesAny.createMedian
	};
	return dependenciesMedian_generated;
}

var hasRequiredDependenciesMad_generated;

function requireDependenciesMad_generated () {
	if (hasRequiredDependenciesMad_generated) return dependenciesMad_generated;
	hasRequiredDependenciesMad_generated = 1;

	Object.defineProperty(dependenciesMad_generated, "__esModule", {
	  value: true
	});
	dependenciesMad_generated.madDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesMedianGenerated = requireDependenciesMedian_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMad_generated.madDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  medianDependencies: _dependenciesMedianGenerated.medianDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMad: _factoriesAny.createMad
	};
	return dependenciesMad_generated;
}

var dependenciesMagneticConstant_generated = {};

var hasRequiredDependenciesMagneticConstant_generated;

function requireDependenciesMagneticConstant_generated () {
	if (hasRequiredDependenciesMagneticConstant_generated) return dependenciesMagneticConstant_generated;
	hasRequiredDependenciesMagneticConstant_generated = 1;

	Object.defineProperty(dependenciesMagneticConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesMagneticConstant_generated.magneticConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMagneticConstant_generated.magneticConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createMagneticConstant: _factoriesAny.createMagneticConstant
	};
	return dependenciesMagneticConstant_generated;
}

var dependenciesMagneticFluxQuantum_generated = {};

var hasRequiredDependenciesMagneticFluxQuantum_generated;

function requireDependenciesMagneticFluxQuantum_generated () {
	if (hasRequiredDependenciesMagneticFluxQuantum_generated) return dependenciesMagneticFluxQuantum_generated;
	hasRequiredDependenciesMagneticFluxQuantum_generated = 1;

	Object.defineProperty(dependenciesMagneticFluxQuantum_generated, "__esModule", {
	  value: true
	});
	dependenciesMagneticFluxQuantum_generated.magneticFluxQuantumDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMagneticFluxQuantum_generated.magneticFluxQuantumDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createMagneticFluxQuantum: _factoriesAny.createMagneticFluxQuantum
	};
	return dependenciesMagneticFluxQuantum_generated;
}

var dependenciesMapSlices_generated = {};

var hasRequiredDependenciesMapSlices_generated;

function requireDependenciesMapSlices_generated () {
	if (hasRequiredDependenciesMapSlices_generated) return dependenciesMapSlices_generated;
	hasRequiredDependenciesMapSlices_generated = 1;

	Object.defineProperty(dependenciesMapSlices_generated, "__esModule", {
	  value: true
	});
	dependenciesMapSlices_generated.mapSlicesDependencies = void 0;
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMapSlices_generated.mapSlicesDependencies = {
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMapSlices: _factoriesAny.createMapSlices
	};
	return dependenciesMapSlices_generated;
}

var dependenciesMapSlicesTransform_generated = {};

var hasRequiredDependenciesMapSlicesTransform_generated;

function requireDependenciesMapSlicesTransform_generated () {
	if (hasRequiredDependenciesMapSlicesTransform_generated) return dependenciesMapSlicesTransform_generated;
	hasRequiredDependenciesMapSlicesTransform_generated = 1;

	Object.defineProperty(dependenciesMapSlicesTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesMapSlicesTransform_generated.mapSlicesTransformDependencies = void 0;
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMapSlicesTransform_generated.mapSlicesTransformDependencies = {
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMapSlicesTransform: _factoriesAny.createMapSlicesTransform
	};
	return dependenciesMapSlicesTransform_generated;
}

var dependenciesMapTransform_generated = {};

var hasRequiredDependenciesMapTransform_generated;

function requireDependenciesMapTransform_generated () {
	if (hasRequiredDependenciesMapTransform_generated) return dependenciesMapTransform_generated;
	hasRequiredDependenciesMapTransform_generated = 1;

	Object.defineProperty(dependenciesMapTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesMapTransform_generated.mapTransformDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMapTransform_generated.mapTransformDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMapTransform: _factoriesAny.createMapTransform
	};
	return dependenciesMapTransform_generated;
}

var dependenciesMatrixFromFunction_generated = {};

var hasRequiredDependenciesMatrixFromFunction_generated;

function requireDependenciesMatrixFromFunction_generated () {
	if (hasRequiredDependenciesMatrixFromFunction_generated) return dependenciesMatrixFromFunction_generated;
	hasRequiredDependenciesMatrixFromFunction_generated = 1;

	Object.defineProperty(dependenciesMatrixFromFunction_generated, "__esModule", {
	  value: true
	});
	dependenciesMatrixFromFunction_generated.matrixFromFunctionDependencies = void 0;
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMatrixFromFunction_generated.matrixFromFunctionDependencies = {
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMatrixFromFunction: _factoriesAny.createMatrixFromFunction
	};
	return dependenciesMatrixFromFunction_generated;
}

var dependenciesMatrixFromRows_generated = {};

var hasRequiredDependenciesMatrixFromRows_generated;

function requireDependenciesMatrixFromRows_generated () {
	if (hasRequiredDependenciesMatrixFromRows_generated) return dependenciesMatrixFromRows_generated;
	hasRequiredDependenciesMatrixFromRows_generated = 1;

	Object.defineProperty(dependenciesMatrixFromRows_generated, "__esModule", {
	  value: true
	});
	dependenciesMatrixFromRows_generated.matrixFromRowsDependencies = void 0;
	var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMatrixFromRows_generated.matrixFromRowsDependencies = {
	  flattenDependencies: _dependenciesFlattenGenerated.flattenDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMatrixFromRows: _factoriesAny.createMatrixFromRows
	};
	return dependenciesMatrixFromRows_generated;
}

var dependenciesMax_generated = {};

var hasRequiredDependenciesMax_generated;

function requireDependenciesMax_generated () {
	if (hasRequiredDependenciesMax_generated) return dependenciesMax_generated;
	hasRequiredDependenciesMax_generated = 1;

	Object.defineProperty(dependenciesMax_generated, "__esModule", {
	  value: true
	});
	dependenciesMax_generated.maxDependencies = void 0;
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMax_generated.maxDependencies = {
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMax: _factoriesAny.createMax
	};
	return dependenciesMax_generated;
}

var dependenciesMaxTransform_generated = {};

var hasRequiredDependenciesMaxTransform_generated;

function requireDependenciesMaxTransform_generated () {
	if (hasRequiredDependenciesMaxTransform_generated) return dependenciesMaxTransform_generated;
	hasRequiredDependenciesMaxTransform_generated = 1;

	Object.defineProperty(dependenciesMaxTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesMaxTransform_generated.maxTransformDependencies = void 0;
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMaxTransform_generated.maxTransformDependencies = {
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMaxTransform: _factoriesAny.createMaxTransform
	};
	return dependenciesMaxTransform_generated;
}

var dependenciesMeanTransform_generated = {};

var hasRequiredDependenciesMeanTransform_generated;

function requireDependenciesMeanTransform_generated () {
	if (hasRequiredDependenciesMeanTransform_generated) return dependenciesMeanTransform_generated;
	hasRequiredDependenciesMeanTransform_generated = 1;

	Object.defineProperty(dependenciesMeanTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesMeanTransform_generated.meanTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMeanTransform_generated.meanTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMeanTransform: _factoriesAny.createMeanTransform
	};
	return dependenciesMeanTransform_generated;
}

var dependenciesMin_generated = {};

var hasRequiredDependenciesMin_generated;

function requireDependenciesMin_generated () {
	if (hasRequiredDependenciesMin_generated) return dependenciesMin_generated;
	hasRequiredDependenciesMin_generated = 1;

	Object.defineProperty(dependenciesMin_generated, "__esModule", {
	  value: true
	});
	dependenciesMin_generated.minDependencies = void 0;
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMin_generated.minDependencies = {
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMin: _factoriesAny.createMin
	};
	return dependenciesMin_generated;
}

var dependenciesMinTransform_generated = {};

var hasRequiredDependenciesMinTransform_generated;

function requireDependenciesMinTransform_generated () {
	if (hasRequiredDependenciesMinTransform_generated) return dependenciesMinTransform_generated;
	hasRequiredDependenciesMinTransform_generated = 1;

	Object.defineProperty(dependenciesMinTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesMinTransform_generated.minTransformDependencies = void 0;
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMinTransform_generated.minTransformDependencies = {
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMinTransform: _factoriesAny.createMinTransform
	};
	return dependenciesMinTransform_generated;
}

var dependenciesMode_generated = {};

var hasRequiredDependenciesMode_generated;

function requireDependenciesMode_generated () {
	if (hasRequiredDependenciesMode_generated) return dependenciesMode_generated;
	hasRequiredDependenciesMode_generated = 1;

	Object.defineProperty(dependenciesMode_generated, "__esModule", {
	  value: true
	});
	dependenciesMode_generated.modeDependencies = void 0;
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMode_generated.modeDependencies = {
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  isNumericDependencies: _dependenciesIsNumericGenerated.isNumericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMode: _factoriesAny.createMode
	};
	return dependenciesMode_generated;
}

var dependenciesMolarMass_generated = {};

var hasRequiredDependenciesMolarMass_generated;

function requireDependenciesMolarMass_generated () {
	if (hasRequiredDependenciesMolarMass_generated) return dependenciesMolarMass_generated;
	hasRequiredDependenciesMolarMass_generated = 1;

	Object.defineProperty(dependenciesMolarMass_generated, "__esModule", {
	  value: true
	});
	dependenciesMolarMass_generated.molarMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMolarMass_generated.molarMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createMolarMass: _factoriesAny.createMolarMass
	};
	return dependenciesMolarMass_generated;
}

var dependenciesMolarMassC12_generated = {};

var hasRequiredDependenciesMolarMassC12_generated;

function requireDependenciesMolarMassC12_generated () {
	if (hasRequiredDependenciesMolarMassC12_generated) return dependenciesMolarMassC12_generated;
	hasRequiredDependenciesMolarMassC12_generated = 1;

	Object.defineProperty(dependenciesMolarMassC12_generated, "__esModule", {
	  value: true
	});
	dependenciesMolarMassC12_generated.molarMassC12Dependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMolarMassC12_generated.molarMassC12Dependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createMolarMassC12: _factoriesAny.createMolarMassC12
	};
	return dependenciesMolarMassC12_generated;
}

var dependenciesMolarPlanckConstant_generated = {};

var hasRequiredDependenciesMolarPlanckConstant_generated;

function requireDependenciesMolarPlanckConstant_generated () {
	if (hasRequiredDependenciesMolarPlanckConstant_generated) return dependenciesMolarPlanckConstant_generated;
	hasRequiredDependenciesMolarPlanckConstant_generated = 1;

	Object.defineProperty(dependenciesMolarPlanckConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesMolarPlanckConstant_generated.molarPlanckConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMolarPlanckConstant_generated.molarPlanckConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createMolarPlanckConstant: _factoriesAny.createMolarPlanckConstant
	};
	return dependenciesMolarPlanckConstant_generated;
}

var dependenciesMolarVolume_generated = {};

var hasRequiredDependenciesMolarVolume_generated;

function requireDependenciesMolarVolume_generated () {
	if (hasRequiredDependenciesMolarVolume_generated) return dependenciesMolarVolume_generated;
	hasRequiredDependenciesMolarVolume_generated = 1;

	Object.defineProperty(dependenciesMolarVolume_generated, "__esModule", {
	  value: true
	});
	dependenciesMolarVolume_generated.molarVolumeDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMolarVolume_generated.molarVolumeDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createMolarVolume: _factoriesAny.createMolarVolume
	};
	return dependenciesMolarVolume_generated;
}

var dependenciesMultinomial_generated = {};

var hasRequiredDependenciesMultinomial_generated;

function requireDependenciesMultinomial_generated () {
	if (hasRequiredDependenciesMultinomial_generated) return dependenciesMultinomial_generated;
	hasRequiredDependenciesMultinomial_generated = 1;

	Object.defineProperty(dependenciesMultinomial_generated, "__esModule", {
	  value: true
	});
	dependenciesMultinomial_generated.multinomialDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesFactorialGenerated = requireDependenciesFactorial_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesMultinomial_generated.multinomialDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  factorialDependencies: _dependenciesFactorialGenerated.factorialDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  isPositiveDependencies: _dependenciesIsPositiveGenerated.isPositiveDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createMultinomial: _factoriesAny.createMultinomial
	};
	return dependenciesMultinomial_generated;
}

var dependenciesNaN_generated = {};

var hasRequiredDependenciesNaN_generated;

function requireDependenciesNaN_generated () {
	if (hasRequiredDependenciesNaN_generated) return dependenciesNaN_generated;
	hasRequiredDependenciesNaN_generated = 1;

	Object.defineProperty(dependenciesNaN_generated, "__esModule", {
	  value: true
	});
	dependenciesNaN_generated.NaNDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNaN_generated.NaNDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createNaN: _factoriesAny.createNaN
	};
	return dependenciesNaN_generated;
}

var dependenciesNeutronMass_generated = {};

var hasRequiredDependenciesNeutronMass_generated;

function requireDependenciesNeutronMass_generated () {
	if (hasRequiredDependenciesNeutronMass_generated) return dependenciesNeutronMass_generated;
	hasRequiredDependenciesNeutronMass_generated = 1;

	Object.defineProperty(dependenciesNeutronMass_generated, "__esModule", {
	  value: true
	});
	dependenciesNeutronMass_generated.neutronMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNeutronMass_generated.neutronMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createNeutronMass: _factoriesAny.createNeutronMass
	};
	return dependenciesNeutronMass_generated;
}

var dependenciesNthRoot_generated = {};

var hasRequiredDependenciesNthRoot_generated;

function requireDependenciesNthRoot_generated () {
	if (hasRequiredDependenciesNthRoot_generated) return dependenciesNthRoot_generated;
	hasRequiredDependenciesNthRoot_generated = 1;

	Object.defineProperty(dependenciesNthRoot_generated, "__esModule", {
	  value: true
	});
	dependenciesNthRoot_generated.nthRootDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNthRoot_generated.nthRootDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNthRoot: _factoriesAny.createNthRoot
	};
	return dependenciesNthRoot_generated;
}

var dependenciesNthRoots_generated = {};

var hasRequiredDependenciesNthRoots_generated;

function requireDependenciesNthRoots_generated () {
	if (hasRequiredDependenciesNthRoots_generated) return dependenciesNthRoots_generated;
	hasRequiredDependenciesNthRoots_generated = 1;

	Object.defineProperty(dependenciesNthRoots_generated, "__esModule", {
	  value: true
	});
	dependenciesNthRoots_generated.nthRootsDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNthRoots_generated.nthRootsDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNthRoots: _factoriesAny.createNthRoots
	};
	return dependenciesNthRoots_generated;
}

var dependenciesNuclearMagneton_generated = {};

var hasRequiredDependenciesNuclearMagneton_generated;

function requireDependenciesNuclearMagneton_generated () {
	if (hasRequiredDependenciesNuclearMagneton_generated) return dependenciesNuclearMagneton_generated;
	hasRequiredDependenciesNuclearMagneton_generated = 1;

	Object.defineProperty(dependenciesNuclearMagneton_generated, "__esModule", {
	  value: true
	});
	dependenciesNuclearMagneton_generated.nuclearMagnetonDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNuclearMagneton_generated.nuclearMagnetonDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createNuclearMagneton: _factoriesAny.createNuclearMagneton
	};
	return dependenciesNuclearMagneton_generated;
}

var dependenciesNull_generated = {};

var hasRequiredDependenciesNull_generated;

function requireDependenciesNull_generated () {
	if (hasRequiredDependenciesNull_generated) return dependenciesNull_generated;
	hasRequiredDependenciesNull_generated = 1;

	Object.defineProperty(dependenciesNull_generated, "__esModule", {
	  value: true
	});
	dependenciesNull_generated.nullDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNull_generated.nullDependencies = {
	  createNull: _factoriesAny.createNull
	};
	return dependenciesNull_generated;
}

var dependenciesNullish_generated = {};

var hasRequiredDependenciesNullish_generated;

function requireDependenciesNullish_generated () {
	if (hasRequiredDependenciesNullish_generated) return dependenciesNullish_generated;
	hasRequiredDependenciesNullish_generated = 1;

	Object.defineProperty(dependenciesNullish_generated, "__esModule", {
	  value: true
	});
	dependenciesNullish_generated.nullishDependencies = void 0;
	var _dependenciesDeepEqualGenerated = requireDependenciesDeepEqual_generated();
	var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNullish_generated.nullishDependencies = {
	  deepEqualDependencies: _dependenciesDeepEqualGenerated.deepEqualDependencies,
	  flattenDependencies: _dependenciesFlattenGenerated.flattenDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNullish: _factoriesAny.createNullish
	};
	return dependenciesNullish_generated;
}

var dependenciesNullishTransform_generated = {};

var hasRequiredDependenciesNullishTransform_generated;

function requireDependenciesNullishTransform_generated () {
	if (hasRequiredDependenciesNullishTransform_generated) return dependenciesNullishTransform_generated;
	hasRequiredDependenciesNullishTransform_generated = 1;

	Object.defineProperty(dependenciesNullishTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesNullishTransform_generated.nullishTransformDependencies = void 0;
	var _dependenciesDeepEqualGenerated = requireDependenciesDeepEqual_generated();
	var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNullishTransform_generated.nullishTransformDependencies = {
	  deepEqualDependencies: _dependenciesDeepEqualGenerated.deepEqualDependencies,
	  flattenDependencies: _dependenciesFlattenGenerated.flattenDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNullishTransform: _factoriesAny.createNullishTransform
	};
	return dependenciesNullishTransform_generated;
}

var dependenciesNum_generated = {};

var hasRequiredDependenciesNum_generated;

function requireDependenciesNum_generated () {
	if (hasRequiredDependenciesNum_generated) return dependenciesNum_generated;
	hasRequiredDependenciesNum_generated = 1;

	Object.defineProperty(dependenciesNum_generated, "__esModule", {
	  value: true
	});
	dependenciesNum_generated.numDependencies = void 0;
	var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesNum_generated.numDependencies = {
	  fractionDependencies: _dependenciesFractionGenerated.fractionDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createNum: _factoriesAny.createNum
	};
	return dependenciesNum_generated;
}

var dependenciesOct_generated = {};

var hasRequiredDependenciesOct_generated;

function requireDependenciesOct_generated () {
	if (hasRequiredDependenciesOct_generated) return dependenciesOct_generated;
	hasRequiredDependenciesOct_generated = 1;

	Object.defineProperty(dependenciesOct_generated, "__esModule", {
	  value: true
	});
	dependenciesOct_generated.octDependencies = void 0;
	var _dependenciesFormatGenerated = requireDependenciesFormat_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesOct_generated.octDependencies = {
	  formatDependencies: _dependenciesFormatGenerated.formatDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createOct: _factoriesAny.createOct
	};
	return dependenciesOct_generated;
}

var dependenciesOnes_generated = {};

var hasRequiredDependenciesOnes_generated;

function requireDependenciesOnes_generated () {
	if (hasRequiredDependenciesOnes_generated) return dependenciesOnes_generated;
	hasRequiredDependenciesOnes_generated = 1;

	Object.defineProperty(dependenciesOnes_generated, "__esModule", {
	  value: true
	});
	dependenciesOnes_generated.onesDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesOnes_generated.onesDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createOnes: _factoriesAny.createOnes
	};
	return dependenciesOnes_generated;
}

var dependenciesOr_generated = {};

var hasRequiredDependenciesOr_generated;

function requireDependenciesOr_generated () {
	if (hasRequiredDependenciesOr_generated) return dependenciesOr_generated;
	hasRequiredDependenciesOr_generated = 1;

	Object.defineProperty(dependenciesOr_generated, "__esModule", {
	  value: true
	});
	dependenciesOr_generated.orDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesOr_generated.orDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createOr: _factoriesAny.createOr
	};
	return dependenciesOr_generated;
}

var dependenciesOrTransform_generated = {};

var hasRequiredDependenciesOrTransform_generated;

function requireDependenciesOrTransform_generated () {
	if (hasRequiredDependenciesOrTransform_generated) return dependenciesOrTransform_generated;
	hasRequiredDependenciesOrTransform_generated = 1;

	Object.defineProperty(dependenciesOrTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesOrTransform_generated.orTransformDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesOrTransform_generated.orTransformDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createOrTransform: _factoriesAny.createOrTransform
	};
	return dependenciesOrTransform_generated;
}

var dependenciesParser_generated = {};

var dependenciesParserClass_generated = {};

var hasRequiredDependenciesParserClass_generated;

function requireDependenciesParserClass_generated () {
	if (hasRequiredDependenciesParserClass_generated) return dependenciesParserClass_generated;
	hasRequiredDependenciesParserClass_generated = 1;

	Object.defineProperty(dependenciesParserClass_generated, "__esModule", {
	  value: true
	});
	dependenciesParserClass_generated.ParserDependencies = void 0;
	var _dependenciesEvaluateGenerated = requireDependenciesEvaluate_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesParserClass_generated.ParserDependencies = {
	  evaluateDependencies: _dependenciesEvaluateGenerated.evaluateDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  createParserClass: _factoriesAny.createParserClass
	};
	return dependenciesParserClass_generated;
}

var hasRequiredDependenciesParser_generated;

function requireDependenciesParser_generated () {
	if (hasRequiredDependenciesParser_generated) return dependenciesParser_generated;
	hasRequiredDependenciesParser_generated = 1;

	Object.defineProperty(dependenciesParser_generated, "__esModule", {
	  value: true
	});
	dependenciesParser_generated.parserDependencies = void 0;
	var _dependenciesParserClassGenerated = requireDependenciesParserClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesParser_generated.parserDependencies = {
	  ParserDependencies: _dependenciesParserClassGenerated.ParserDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createParser: _factoriesAny.createParser
	};
	return dependenciesParser_generated;
}

var dependenciesPermutations_generated = {};

var hasRequiredDependenciesPermutations_generated;

function requireDependenciesPermutations_generated () {
	if (hasRequiredDependenciesPermutations_generated) return dependenciesPermutations_generated;
	hasRequiredDependenciesPermutations_generated = 1;

	Object.defineProperty(dependenciesPermutations_generated, "__esModule", {
	  value: true
	});
	dependenciesPermutations_generated.permutationsDependencies = void 0;
	var _dependenciesFactorialGenerated = requireDependenciesFactorial_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPermutations_generated.permutationsDependencies = {
	  factorialDependencies: _dependenciesFactorialGenerated.factorialDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createPermutations: _factoriesAny.createPermutations
	};
	return dependenciesPermutations_generated;
}

var dependenciesPhi_generated = {};

var hasRequiredDependenciesPhi_generated;

function requireDependenciesPhi_generated () {
	if (hasRequiredDependenciesPhi_generated) return dependenciesPhi_generated;
	hasRequiredDependenciesPhi_generated = 1;

	Object.defineProperty(dependenciesPhi_generated, "__esModule", {
	  value: true
	});
	dependenciesPhi_generated.phiDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPhi_generated.phiDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createPhi: _factoriesAny.createPhi
	};
	return dependenciesPhi_generated;
}

var dependenciesPi_generated = {};

var hasRequiredDependenciesPi_generated;

function requireDependenciesPi_generated () {
	if (hasRequiredDependenciesPi_generated) return dependenciesPi_generated;
	hasRequiredDependenciesPi_generated = 1;

	Object.defineProperty(dependenciesPi_generated, "__esModule", {
	  value: true
	});
	dependenciesPi_generated.piDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPi_generated.piDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createPi: _factoriesAny.createPi
	};
	return dependenciesPi_generated;
}

var dependenciesPickRandom_generated = {};

var hasRequiredDependenciesPickRandom_generated;

function requireDependenciesPickRandom_generated () {
	if (hasRequiredDependenciesPickRandom_generated) return dependenciesPickRandom_generated;
	hasRequiredDependenciesPickRandom_generated = 1;

	Object.defineProperty(dependenciesPickRandom_generated, "__esModule", {
	  value: true
	});
	dependenciesPickRandom_generated.pickRandomDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPickRandom_generated.pickRandomDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createPickRandom: _factoriesAny.createPickRandom
	};
	return dependenciesPickRandom_generated;
}

var dependenciesPinv_generated = {};

var hasRequiredDependenciesPinv_generated;

function requireDependenciesPinv_generated () {
	if (hasRequiredDependenciesPinv_generated) return dependenciesPinv_generated;
	hasRequiredDependenciesPinv_generated = 1;

	Object.defineProperty(dependenciesPinv_generated, "__esModule", {
	  value: true
	});
	dependenciesPinv_generated.pinvDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesCtransposeGenerated = requireDependenciesCtranspose_generated();
	var _dependenciesDeepEqualGenerated = requireDependenciesDeepEqual_generated();
	var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
	var _dependenciesDotGenerated = requireDependenciesDot_generated();
	var _dependenciesDotDivideGenerated = requireDependenciesDotDivide_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesInvGenerated = requireDependenciesInv_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPinv_generated.pinvDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  ctransposeDependencies: _dependenciesCtransposeGenerated.ctransposeDependencies,
	  deepEqualDependencies: _dependenciesDeepEqualGenerated.deepEqualDependencies,
	  divideScalarDependencies: _dependenciesDivideScalarGenerated.divideScalarDependencies,
	  dotDependencies: _dependenciesDotGenerated.dotDependencies,
	  dotDivideDependencies: _dependenciesDotDivideGenerated.dotDivideDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  invDependencies: _dependenciesInvGenerated.invDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createPinv: _factoriesAny.createPinv
	};
	return dependenciesPinv_generated;
}

var dependenciesPlanckCharge_generated = {};

var hasRequiredDependenciesPlanckCharge_generated;

function requireDependenciesPlanckCharge_generated () {
	if (hasRequiredDependenciesPlanckCharge_generated) return dependenciesPlanckCharge_generated;
	hasRequiredDependenciesPlanckCharge_generated = 1;

	Object.defineProperty(dependenciesPlanckCharge_generated, "__esModule", {
	  value: true
	});
	dependenciesPlanckCharge_generated.planckChargeDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPlanckCharge_generated.planckChargeDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createPlanckCharge: _factoriesAny.createPlanckCharge
	};
	return dependenciesPlanckCharge_generated;
}

var dependenciesPlanckConstant_generated = {};

var hasRequiredDependenciesPlanckConstant_generated;

function requireDependenciesPlanckConstant_generated () {
	if (hasRequiredDependenciesPlanckConstant_generated) return dependenciesPlanckConstant_generated;
	hasRequiredDependenciesPlanckConstant_generated = 1;

	Object.defineProperty(dependenciesPlanckConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesPlanckConstant_generated.planckConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPlanckConstant_generated.planckConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createPlanckConstant: _factoriesAny.createPlanckConstant
	};
	return dependenciesPlanckConstant_generated;
}

var dependenciesPlanckLength_generated = {};

var hasRequiredDependenciesPlanckLength_generated;

function requireDependenciesPlanckLength_generated () {
	if (hasRequiredDependenciesPlanckLength_generated) return dependenciesPlanckLength_generated;
	hasRequiredDependenciesPlanckLength_generated = 1;

	Object.defineProperty(dependenciesPlanckLength_generated, "__esModule", {
	  value: true
	});
	dependenciesPlanckLength_generated.planckLengthDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPlanckLength_generated.planckLengthDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createPlanckLength: _factoriesAny.createPlanckLength
	};
	return dependenciesPlanckLength_generated;
}

var dependenciesPlanckMass_generated = {};

var hasRequiredDependenciesPlanckMass_generated;

function requireDependenciesPlanckMass_generated () {
	if (hasRequiredDependenciesPlanckMass_generated) return dependenciesPlanckMass_generated;
	hasRequiredDependenciesPlanckMass_generated = 1;

	Object.defineProperty(dependenciesPlanckMass_generated, "__esModule", {
	  value: true
	});
	dependenciesPlanckMass_generated.planckMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPlanckMass_generated.planckMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createPlanckMass: _factoriesAny.createPlanckMass
	};
	return dependenciesPlanckMass_generated;
}

var dependenciesPlanckTemperature_generated = {};

var hasRequiredDependenciesPlanckTemperature_generated;

function requireDependenciesPlanckTemperature_generated () {
	if (hasRequiredDependenciesPlanckTemperature_generated) return dependenciesPlanckTemperature_generated;
	hasRequiredDependenciesPlanckTemperature_generated = 1;

	Object.defineProperty(dependenciesPlanckTemperature_generated, "__esModule", {
	  value: true
	});
	dependenciesPlanckTemperature_generated.planckTemperatureDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPlanckTemperature_generated.planckTemperatureDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createPlanckTemperature: _factoriesAny.createPlanckTemperature
	};
	return dependenciesPlanckTemperature_generated;
}

var dependenciesPlanckTime_generated = {};

var hasRequiredDependenciesPlanckTime_generated;

function requireDependenciesPlanckTime_generated () {
	if (hasRequiredDependenciesPlanckTime_generated) return dependenciesPlanckTime_generated;
	hasRequiredDependenciesPlanckTime_generated = 1;

	Object.defineProperty(dependenciesPlanckTime_generated, "__esModule", {
	  value: true
	});
	dependenciesPlanckTime_generated.planckTimeDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPlanckTime_generated.planckTimeDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createPlanckTime: _factoriesAny.createPlanckTime
	};
	return dependenciesPlanckTime_generated;
}

var dependenciesPolynomialRoot_generated = {};

var hasRequiredDependenciesPolynomialRoot_generated;

function requireDependenciesPolynomialRoot_generated () {
	if (hasRequiredDependenciesPolynomialRoot_generated) return dependenciesPolynomialRoot_generated;
	hasRequiredDependenciesPolynomialRoot_generated = 1;

	Object.defineProperty(dependenciesPolynomialRoot_generated, "__esModule", {
	  value: true
	});
	dependenciesPolynomialRoot_generated.polynomialRootDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesCbrtGenerated = requireDependenciesCbrt_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesImGenerated = requireDependenciesIm_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesReGenerated = requireDependenciesRe_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypeOfGenerated = requireDependenciesTypeOf_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPolynomialRoot_generated.polynomialRootDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  cbrtDependencies: _dependenciesCbrtGenerated.cbrtDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  imDependencies: _dependenciesImGenerated.imDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  reDependencies: _dependenciesReGenerated.reDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typeOfDependencies: _dependenciesTypeOfGenerated.typeOfDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createPolynomialRoot: _factoriesAny.createPolynomialRoot
	};
	return dependenciesPolynomialRoot_generated;
}

var dependenciesPrint_generated = {};

var hasRequiredDependenciesPrint_generated;

function requireDependenciesPrint_generated () {
	if (hasRequiredDependenciesPrint_generated) return dependenciesPrint_generated;
	hasRequiredDependenciesPrint_generated = 1;

	Object.defineProperty(dependenciesPrint_generated, "__esModule", {
	  value: true
	});
	dependenciesPrint_generated.printDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPrint_generated.printDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createPrint: _factoriesAny.createPrint
	};
	return dependenciesPrint_generated;
}

var dependenciesPrintTransform_generated = {};

var hasRequiredDependenciesPrintTransform_generated;

function requireDependenciesPrintTransform_generated () {
	if (hasRequiredDependenciesPrintTransform_generated) return dependenciesPrintTransform_generated;
	hasRequiredDependenciesPrintTransform_generated = 1;

	Object.defineProperty(dependenciesPrintTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesPrintTransform_generated.printTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesPrintTransform_generated.printTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createPrintTransform: _factoriesAny.createPrintTransform
	};
	return dependenciesPrintTransform_generated;
}

var dependenciesProtonMass_generated = {};

var hasRequiredDependenciesProtonMass_generated;

function requireDependenciesProtonMass_generated () {
	if (hasRequiredDependenciesProtonMass_generated) return dependenciesProtonMass_generated;
	hasRequiredDependenciesProtonMass_generated = 1;

	Object.defineProperty(dependenciesProtonMass_generated, "__esModule", {
	  value: true
	});
	dependenciesProtonMass_generated.protonMassDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesProtonMass_generated.protonMassDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createProtonMass: _factoriesAny.createProtonMass
	};
	return dependenciesProtonMass_generated;
}

var dependenciesQuantileSeq_generated = {};

var hasRequiredDependenciesQuantileSeq_generated;

function requireDependenciesQuantileSeq_generated () {
	if (hasRequiredDependenciesQuantileSeq_generated) return dependenciesQuantileSeq_generated;
	hasRequiredDependenciesQuantileSeq_generated = 1;

	Object.defineProperty(dependenciesQuantileSeq_generated, "__esModule", {
	  value: true
	});
	dependenciesQuantileSeq_generated.quantileSeqDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMapSlicesGenerated = requireDependenciesMapSlices_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesPartitionSelectGenerated = requireDependenciesPartitionSelect_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSmallerEqGenerated = requireDependenciesSmallerEq_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesQuantileSeq_generated.quantileSeqDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  compareDependencies: _dependenciesCompareGenerated.compareDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  mapSlicesDependencies: _dependenciesMapSlicesGenerated.mapSlicesDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  partitionSelectDependencies: _dependenciesPartitionSelectGenerated.partitionSelectDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  smallerEqDependencies: _dependenciesSmallerEqGenerated.smallerEqDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createQuantileSeq: _factoriesAny.createQuantileSeq
	};
	return dependenciesQuantileSeq_generated;
}

var dependenciesQuantileSeqTransform_generated = {};

var hasRequiredDependenciesQuantileSeqTransform_generated;

function requireDependenciesQuantileSeqTransform_generated () {
	if (hasRequiredDependenciesQuantileSeqTransform_generated) return dependenciesQuantileSeqTransform_generated;
	hasRequiredDependenciesQuantileSeqTransform_generated = 1;

	Object.defineProperty(dependenciesQuantileSeqTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesQuantileSeqTransform_generated.quantileSeqTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMapSlicesGenerated = requireDependenciesMapSlices_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesPartitionSelectGenerated = requireDependenciesPartitionSelect_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSmallerEqGenerated = requireDependenciesSmallerEq_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesQuantileSeqTransform_generated.quantileSeqTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  compareDependencies: _dependenciesCompareGenerated.compareDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  isIntegerDependencies: _dependenciesIsIntegerGenerated.isIntegerDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  mapSlicesDependencies: _dependenciesMapSlicesGenerated.mapSlicesDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  partitionSelectDependencies: _dependenciesPartitionSelectGenerated.partitionSelectDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  smallerEqDependencies: _dependenciesSmallerEqGenerated.smallerEqDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createQuantileSeqTransform: _factoriesAny.createQuantileSeqTransform
	};
	return dependenciesQuantileSeqTransform_generated;
}

var dependenciesQuantumOfCirculation_generated = {};

var hasRequiredDependenciesQuantumOfCirculation_generated;

function requireDependenciesQuantumOfCirculation_generated () {
	if (hasRequiredDependenciesQuantumOfCirculation_generated) return dependenciesQuantumOfCirculation_generated;
	hasRequiredDependenciesQuantumOfCirculation_generated = 1;

	Object.defineProperty(dependenciesQuantumOfCirculation_generated, "__esModule", {
	  value: true
	});
	dependenciesQuantumOfCirculation_generated.quantumOfCirculationDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesQuantumOfCirculation_generated.quantumOfCirculationDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createQuantumOfCirculation: _factoriesAny.createQuantumOfCirculation
	};
	return dependenciesQuantumOfCirculation_generated;
}

var dependenciesRandom_generated = {};

var hasRequiredDependenciesRandom_generated;

function requireDependenciesRandom_generated () {
	if (hasRequiredDependenciesRandom_generated) return dependenciesRandom_generated;
	hasRequiredDependenciesRandom_generated = 1;

	Object.defineProperty(dependenciesRandom_generated, "__esModule", {
	  value: true
	});
	dependenciesRandom_generated.randomDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRandom_generated.randomDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRandom: _factoriesAny.createRandom
	};
	return dependenciesRandom_generated;
}

var dependenciesRandomInt_generated = {};

var hasRequiredDependenciesRandomInt_generated;

function requireDependenciesRandomInt_generated () {
	if (hasRequiredDependenciesRandomInt_generated) return dependenciesRandomInt_generated;
	hasRequiredDependenciesRandomInt_generated = 1;

	Object.defineProperty(dependenciesRandomInt_generated, "__esModule", {
	  value: true
	});
	dependenciesRandomInt_generated.randomIntDependencies = void 0;
	var _dependenciesLog2Generated = requireDependenciesLog2_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRandomInt_generated.randomIntDependencies = {
	  log2Dependencies: _dependenciesLog2Generated.log2Dependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRandomInt: _factoriesAny.createRandomInt
	};
	return dependenciesRandomInt_generated;
}

var dependenciesRangeClass_generated = {};

var hasRequiredDependenciesRangeClass_generated;

function requireDependenciesRangeClass_generated () {
	if (hasRequiredDependenciesRangeClass_generated) return dependenciesRangeClass_generated;
	hasRequiredDependenciesRangeClass_generated = 1;

	Object.defineProperty(dependenciesRangeClass_generated, "__esModule", {
	  value: true
	});
	dependenciesRangeClass_generated.RangeDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRangeClass_generated.RangeDependencies = {
	  createRangeClass: _factoriesAny.createRangeClass
	};
	return dependenciesRangeClass_generated;
}

var dependenciesRangeTransform_generated = {};

var hasRequiredDependenciesRangeTransform_generated;

function requireDependenciesRangeTransform_generated () {
	if (hasRequiredDependenciesRangeTransform_generated) return dependenciesRangeTransform_generated;
	hasRequiredDependenciesRangeTransform_generated = 1;

	Object.defineProperty(dependenciesRangeTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesRangeTransform_generated.rangeTransformDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesLargerEqGenerated = requireDependenciesLargerEq_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSmallerEqGenerated = requireDependenciesSmallerEq_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRangeTransform_generated.rangeTransformDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  isPositiveDependencies: _dependenciesIsPositiveGenerated.isPositiveDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  largerEqDependencies: _dependenciesLargerEqGenerated.largerEqDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  smallerEqDependencies: _dependenciesSmallerEqGenerated.smallerEqDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRangeTransform: _factoriesAny.createRangeTransform
	};
	return dependenciesRangeTransform_generated;
}

var dependenciesRationalize_generated = {};

var hasRequiredDependenciesRationalize_generated;

function requireDependenciesRationalize_generated () {
	if (hasRequiredDependenciesRationalize_generated) return dependenciesRationalize_generated;
	hasRequiredDependenciesRationalize_generated = 1;

	Object.defineProperty(dependenciesRationalize_generated, "__esModule", {
	  value: true
	});
	dependenciesRationalize_generated.rationalizeDependencies = void 0;
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
	var _dependenciesAccessorNodeGenerated = requireDependenciesAccessorNode_generated();
	var _dependenciesArrayNodeGenerated = requireDependenciesArrayNode_generated();
	var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
	var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
	var _dependenciesIndexNodeGenerated = requireDependenciesIndexNode_generated();
	var _dependenciesObjectNodeGenerated = requireDependenciesObjectNode_generated();
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
	var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesSimplifyGenerated = requireDependenciesSimplify_generated();
	var _dependenciesSimplifyConstantGenerated = requireDependenciesSimplifyConstant_generated();
	var _dependenciesSimplifyCoreGenerated = requireDependenciesSimplifyCore_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRationalize_generated.rationalizeDependencies = {
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  fractionDependencies: _dependenciesFractionGenerated.fractionDependencies,
	  AccessorNodeDependencies: _dependenciesAccessorNodeGenerated.AccessorNodeDependencies,
	  ArrayNodeDependencies: _dependenciesArrayNodeGenerated.ArrayNodeDependencies,
	  ConstantNodeDependencies: _dependenciesConstantNodeGenerated.ConstantNodeDependencies,
	  FunctionNodeDependencies: _dependenciesFunctionNodeGenerated.FunctionNodeDependencies,
	  IndexNodeDependencies: _dependenciesIndexNodeGenerated.IndexNodeDependencies,
	  ObjectNodeDependencies: _dependenciesObjectNodeGenerated.ObjectNodeDependencies,
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  ParenthesisNodeDependencies: _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies,
	  SymbolNodeDependencies: _dependenciesSymbolNodeGenerated.SymbolNodeDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  isZeroDependencies: _dependenciesIsZeroGenerated.isZeroDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  simplifyDependencies: _dependenciesSimplifyGenerated.simplifyDependencies,
	  simplifyConstantDependencies: _dependenciesSimplifyConstantGenerated.simplifyConstantDependencies,
	  simplifyCoreDependencies: _dependenciesSimplifyCoreGenerated.simplifyCoreDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRationalize: _factoriesAny.createRationalize
	};
	return dependenciesRationalize_generated;
}

var dependenciesReducedPlanckConstant_generated = {};

var hasRequiredDependenciesReducedPlanckConstant_generated;

function requireDependenciesReducedPlanckConstant_generated () {
	if (hasRequiredDependenciesReducedPlanckConstant_generated) return dependenciesReducedPlanckConstant_generated;
	hasRequiredDependenciesReducedPlanckConstant_generated = 1;

	Object.defineProperty(dependenciesReducedPlanckConstant_generated, "__esModule", {
	  value: true
	});
	dependenciesReducedPlanckConstant_generated.reducedPlanckConstantDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesReducedPlanckConstant_generated.reducedPlanckConstantDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createReducedPlanckConstant: _factoriesAny.createReducedPlanckConstant
	};
	return dependenciesReducedPlanckConstant_generated;
}

var dependenciesResize_generated = {};

var hasRequiredDependenciesResize_generated;

function requireDependenciesResize_generated () {
	if (hasRequiredDependenciesResize_generated) return dependenciesResize_generated;
	hasRequiredDependenciesResize_generated = 1;

	Object.defineProperty(dependenciesResize_generated, "__esModule", {
	  value: true
	});
	dependenciesResize_generated.resizeDependencies = void 0;
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesResize_generated.resizeDependencies = {
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  createResize: _factoriesAny.createResize
	};
	return dependenciesResize_generated;
}

var dependenciesReviver_generated = {};

var hasRequiredDependenciesReviver_generated;

function requireDependenciesReviver_generated () {
	if (hasRequiredDependenciesReviver_generated) return dependenciesReviver_generated;
	hasRequiredDependenciesReviver_generated = 1;

	Object.defineProperty(dependenciesReviver_generated, "__esModule", {
	  value: true
	});
	dependenciesReviver_generated.reviverDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesReviver_generated.reviverDependencies = {
	  createReviver: _factoriesAny.createReviver
	};
	return dependenciesReviver_generated;
}

var dependenciesRightArithShift_generated = {};

var hasRequiredDependenciesRightArithShift_generated;

function requireDependenciesRightArithShift_generated () {
	if (hasRequiredDependenciesRightArithShift_generated) return dependenciesRightArithShift_generated;
	hasRequiredDependenciesRightArithShift_generated = 1;

	Object.defineProperty(dependenciesRightArithShift_generated, "__esModule", {
	  value: true
	});
	dependenciesRightArithShift_generated.rightArithShiftDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRightArithShift_generated.rightArithShiftDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createRightArithShift: _factoriesAny.createRightArithShift
	};
	return dependenciesRightArithShift_generated;
}

var dependenciesRightLogShift_generated = {};

var hasRequiredDependenciesRightLogShift_generated;

function requireDependenciesRightLogShift_generated () {
	if (hasRequiredDependenciesRightLogShift_generated) return dependenciesRightLogShift_generated;
	hasRequiredDependenciesRightLogShift_generated = 1;

	Object.defineProperty(dependenciesRightLogShift_generated, "__esModule", {
	  value: true
	});
	dependenciesRightLogShift_generated.rightLogShiftDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRightLogShift_generated.rightLogShiftDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createRightLogShift: _factoriesAny.createRightLogShift
	};
	return dependenciesRightLogShift_generated;
}

var dependenciesRotate_generated = {};

var dependenciesRotationMatrix_generated = {};

var hasRequiredDependenciesRotationMatrix_generated;

function requireDependenciesRotationMatrix_generated () {
	if (hasRequiredDependenciesRotationMatrix_generated) return dependenciesRotationMatrix_generated;
	hasRequiredDependenciesRotationMatrix_generated = 1;

	Object.defineProperty(dependenciesRotationMatrix_generated, "__esModule", {
	  value: true
	});
	dependenciesRotationMatrix_generated.rotationMatrixDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
	var _dependenciesCosGenerated = requireDependenciesCos_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
	var _dependenciesNormGenerated = requireDependenciesNorm_generated();
	var _dependenciesSinGenerated = requireDependenciesSin_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRotationMatrix_generated.rotationMatrixDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  addScalarDependencies: _dependenciesAddScalarGenerated.addScalarDependencies,
	  cosDependencies: _dependenciesCosGenerated.cosDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  multiplyScalarDependencies: _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies,
	  normDependencies: _dependenciesNormGenerated.normDependencies,
	  sinDependencies: _dependenciesSinGenerated.sinDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createRotationMatrix: _factoriesAny.createRotationMatrix
	};
	return dependenciesRotationMatrix_generated;
}

var hasRequiredDependenciesRotate_generated;

function requireDependenciesRotate_generated () {
	if (hasRequiredDependenciesRotate_generated) return dependenciesRotate_generated;
	hasRequiredDependenciesRotate_generated = 1;

	Object.defineProperty(dependenciesRotate_generated, "__esModule", {
	  value: true
	});
	dependenciesRotate_generated.rotateDependencies = void 0;
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesRotationMatrixGenerated = requireDependenciesRotationMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRotate_generated.rotateDependencies = {
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  rotationMatrixDependencies: _dependenciesRotationMatrixGenerated.rotationMatrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRotate: _factoriesAny.createRotate
	};
	return dependenciesRotate_generated;
}

var dependenciesRow_generated = {};

var hasRequiredDependenciesRow_generated;

function requireDependenciesRow_generated () {
	if (hasRequiredDependenciesRow_generated) return dependenciesRow_generated;
	hasRequiredDependenciesRow_generated = 1;

	Object.defineProperty(dependenciesRow_generated, "__esModule", {
	  value: true
	});
	dependenciesRow_generated.rowDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRangeGenerated = requireDependenciesRange_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRow_generated.rowDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  rangeDependencies: _dependenciesRangeGenerated.rangeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRow: _factoriesAny.createRow
	};
	return dependenciesRow_generated;
}

var dependenciesRowTransform_generated = {};

var hasRequiredDependenciesRowTransform_generated;

function requireDependenciesRowTransform_generated () {
	if (hasRequiredDependenciesRowTransform_generated) return dependenciesRowTransform_generated;
	hasRequiredDependenciesRowTransform_generated = 1;

	Object.defineProperty(dependenciesRowTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesRowTransform_generated.rowTransformDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesRangeGenerated = requireDependenciesRange_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRowTransform_generated.rowTransformDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  rangeDependencies: _dependenciesRangeGenerated.rangeDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createRowTransform: _factoriesAny.createRowTransform
	};
	return dependenciesRowTransform_generated;
}

var dependenciesRydberg_generated = {};

var hasRequiredDependenciesRydberg_generated;

function requireDependenciesRydberg_generated () {
	if (hasRequiredDependenciesRydberg_generated) return dependenciesRydberg_generated;
	hasRequiredDependenciesRydberg_generated = 1;

	Object.defineProperty(dependenciesRydberg_generated, "__esModule", {
	  value: true
	});
	dependenciesRydberg_generated.rydbergDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesRydberg_generated.rydbergDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createRydberg: _factoriesAny.createRydberg
	};
	return dependenciesRydberg_generated;
}

var dependenciesSQRT1_2_generated = {};

var hasRequiredDependenciesSQRT1_2_generated;

function requireDependenciesSQRT1_2_generated () {
	if (hasRequiredDependenciesSQRT1_2_generated) return dependenciesSQRT1_2_generated;
	hasRequiredDependenciesSQRT1_2_generated = 1;

	Object.defineProperty(dependenciesSQRT1_2_generated, "__esModule", {
	  value: true
	});
	dependenciesSQRT1_2_generated.SQRT1_2Dependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	// eslint-disable-line camelcase

	dependenciesSQRT1_2_generated.SQRT1_2Dependencies = {
	  // eslint-disable-line camelcase
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createSQRT1_2: _factoriesAny.createSQRT1_2
	};
	return dependenciesSQRT1_2_generated;
}

var dependenciesSQRT2_generated = {};

var hasRequiredDependenciesSQRT2_generated;

function requireDependenciesSQRT2_generated () {
	if (hasRequiredDependenciesSQRT2_generated) return dependenciesSQRT2_generated;
	hasRequiredDependenciesSQRT2_generated = 1;

	Object.defineProperty(dependenciesSQRT2_generated, "__esModule", {
	  value: true
	});
	dependenciesSQRT2_generated.SQRT2Dependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSQRT2_generated.SQRT2Dependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createSQRT2: _factoriesAny.createSQRT2
	};
	return dependenciesSQRT2_generated;
}

var dependenciesSackurTetrode_generated = {};

var hasRequiredDependenciesSackurTetrode_generated;

function requireDependenciesSackurTetrode_generated () {
	if (hasRequiredDependenciesSackurTetrode_generated) return dependenciesSackurTetrode_generated;
	hasRequiredDependenciesSackurTetrode_generated = 1;

	Object.defineProperty(dependenciesSackurTetrode_generated, "__esModule", {
	  value: true
	});
	dependenciesSackurTetrode_generated.sackurTetrodeDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSackurTetrode_generated.sackurTetrodeDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createSackurTetrode: _factoriesAny.createSackurTetrode
	};
	return dependenciesSackurTetrode_generated;
}

var dependenciesSec_generated = {};

var hasRequiredDependenciesSec_generated;

function requireDependenciesSec_generated () {
	if (hasRequiredDependenciesSec_generated) return dependenciesSec_generated;
	hasRequiredDependenciesSec_generated = 1;

	Object.defineProperty(dependenciesSec_generated, "__esModule", {
	  value: true
	});
	dependenciesSec_generated.secDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSec_generated.secDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSec: _factoriesAny.createSec
	};
	return dependenciesSec_generated;
}

var dependenciesSech_generated = {};

var hasRequiredDependenciesSech_generated;

function requireDependenciesSech_generated () {
	if (hasRequiredDependenciesSech_generated) return dependenciesSech_generated;
	hasRequiredDependenciesSech_generated = 1;

	Object.defineProperty(dependenciesSech_generated, "__esModule", {
	  value: true
	});
	dependenciesSech_generated.sechDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSech_generated.sechDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSech: _factoriesAny.createSech
	};
	return dependenciesSech_generated;
}

var dependenciesSecondRadiation_generated = {};

var hasRequiredDependenciesSecondRadiation_generated;

function requireDependenciesSecondRadiation_generated () {
	if (hasRequiredDependenciesSecondRadiation_generated) return dependenciesSecondRadiation_generated;
	hasRequiredDependenciesSecondRadiation_generated = 1;

	Object.defineProperty(dependenciesSecondRadiation_generated, "__esModule", {
	  value: true
	});
	dependenciesSecondRadiation_generated.secondRadiationDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSecondRadiation_generated.secondRadiationDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createSecondRadiation: _factoriesAny.createSecondRadiation
	};
	return dependenciesSecondRadiation_generated;
}

var dependenciesSetCartesian_generated = {};

var hasRequiredDependenciesSetCartesian_generated;

function requireDependenciesSetCartesian_generated () {
	if (hasRequiredDependenciesSetCartesian_generated) return dependenciesSetCartesian_generated;
	hasRequiredDependenciesSetCartesian_generated = 1;

	Object.defineProperty(dependenciesSetCartesian_generated, "__esModule", {
	  value: true
	});
	dependenciesSetCartesian_generated.setCartesianDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetCartesian_generated.setCartesianDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetCartesian: _factoriesAny.createSetCartesian
	};
	return dependenciesSetCartesian_generated;
}

var dependenciesSetDifference_generated = {};

var hasRequiredDependenciesSetDifference_generated;

function requireDependenciesSetDifference_generated () {
	if (hasRequiredDependenciesSetDifference_generated) return dependenciesSetDifference_generated;
	hasRequiredDependenciesSetDifference_generated = 1;

	Object.defineProperty(dependenciesSetDifference_generated, "__esModule", {
	  value: true
	});
	dependenciesSetDifference_generated.setDifferenceDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetDifference_generated.setDifferenceDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetDifference: _factoriesAny.createSetDifference
	};
	return dependenciesSetDifference_generated;
}

var dependenciesSetDistinct_generated = {};

var hasRequiredDependenciesSetDistinct_generated;

function requireDependenciesSetDistinct_generated () {
	if (hasRequiredDependenciesSetDistinct_generated) return dependenciesSetDistinct_generated;
	hasRequiredDependenciesSetDistinct_generated = 1;

	Object.defineProperty(dependenciesSetDistinct_generated, "__esModule", {
	  value: true
	});
	dependenciesSetDistinct_generated.setDistinctDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetDistinct_generated.setDistinctDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetDistinct: _factoriesAny.createSetDistinct
	};
	return dependenciesSetDistinct_generated;
}

var dependenciesSetIntersect_generated = {};

var hasRequiredDependenciesSetIntersect_generated;

function requireDependenciesSetIntersect_generated () {
	if (hasRequiredDependenciesSetIntersect_generated) return dependenciesSetIntersect_generated;
	hasRequiredDependenciesSetIntersect_generated = 1;

	Object.defineProperty(dependenciesSetIntersect_generated, "__esModule", {
	  value: true
	});
	dependenciesSetIntersect_generated.setIntersectDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetIntersect_generated.setIntersectDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetIntersect: _factoriesAny.createSetIntersect
	};
	return dependenciesSetIntersect_generated;
}

var dependenciesSetIsSubset_generated = {};

var hasRequiredDependenciesSetIsSubset_generated;

function requireDependenciesSetIsSubset_generated () {
	if (hasRequiredDependenciesSetIsSubset_generated) return dependenciesSetIsSubset_generated;
	hasRequiredDependenciesSetIsSubset_generated = 1;

	Object.defineProperty(dependenciesSetIsSubset_generated, "__esModule", {
	  value: true
	});
	dependenciesSetIsSubset_generated.setIsSubsetDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetIsSubset_generated.setIsSubsetDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetIsSubset: _factoriesAny.createSetIsSubset
	};
	return dependenciesSetIsSubset_generated;
}

var dependenciesSetMultiplicity_generated = {};

var hasRequiredDependenciesSetMultiplicity_generated;

function requireDependenciesSetMultiplicity_generated () {
	if (hasRequiredDependenciesSetMultiplicity_generated) return dependenciesSetMultiplicity_generated;
	hasRequiredDependenciesSetMultiplicity_generated = 1;

	Object.defineProperty(dependenciesSetMultiplicity_generated, "__esModule", {
	  value: true
	});
	dependenciesSetMultiplicity_generated.setMultiplicityDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetMultiplicity_generated.setMultiplicityDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetMultiplicity: _factoriesAny.createSetMultiplicity
	};
	return dependenciesSetMultiplicity_generated;
}

var dependenciesSetPowerset_generated = {};

var hasRequiredDependenciesSetPowerset_generated;

function requireDependenciesSetPowerset_generated () {
	if (hasRequiredDependenciesSetPowerset_generated) return dependenciesSetPowerset_generated;
	hasRequiredDependenciesSetPowerset_generated = 1;

	Object.defineProperty(dependenciesSetPowerset_generated, "__esModule", {
	  value: true
	});
	dependenciesSetPowerset_generated.setPowersetDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetPowerset_generated.setPowersetDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetPowerset: _factoriesAny.createSetPowerset
	};
	return dependenciesSetPowerset_generated;
}

var dependenciesSetSize_generated = {};

var hasRequiredDependenciesSetSize_generated;

function requireDependenciesSetSize_generated () {
	if (hasRequiredDependenciesSetSize_generated) return dependenciesSetSize_generated;
	hasRequiredDependenciesSetSize_generated = 1;

	Object.defineProperty(dependenciesSetSize_generated, "__esModule", {
	  value: true
	});
	dependenciesSetSize_generated.setSizeDependencies = void 0;
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetSize_generated.setSizeDependencies = {
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetSize: _factoriesAny.createSetSize
	};
	return dependenciesSetSize_generated;
}

var dependenciesSetSymDifference_generated = {};

var hasRequiredDependenciesSetSymDifference_generated;

function requireDependenciesSetSymDifference_generated () {
	if (hasRequiredDependenciesSetSymDifference_generated) return dependenciesSetSymDifference_generated;
	hasRequiredDependenciesSetSymDifference_generated = 1;

	Object.defineProperty(dependenciesSetSymDifference_generated, "__esModule", {
	  value: true
	});
	dependenciesSetSymDifference_generated.setSymDifferenceDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesSetDifferenceGenerated = requireDependenciesSetDifference_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetSymDifference_generated.setSymDifferenceDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  setDifferenceDependencies: _dependenciesSetDifferenceGenerated.setDifferenceDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetSymDifference: _factoriesAny.createSetSymDifference
	};
	return dependenciesSetSymDifference_generated;
}

var dependenciesSetUnion_generated = {};

var hasRequiredDependenciesSetUnion_generated;

function requireDependenciesSetUnion_generated () {
	if (hasRequiredDependenciesSetUnion_generated) return dependenciesSetUnion_generated;
	hasRequiredDependenciesSetUnion_generated = 1;

	Object.defineProperty(dependenciesSetUnion_generated, "__esModule", {
	  value: true
	});
	dependenciesSetUnion_generated.setUnionDependencies = void 0;
	var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesSetIntersectGenerated = requireDependenciesSetIntersect_generated();
	var _dependenciesSetSymDifferenceGenerated = requireDependenciesSetSymDifference_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSetUnion_generated.setUnionDependencies = {
	  IndexDependencies: _dependenciesIndexClassGenerated.IndexDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  setIntersectDependencies: _dependenciesSetIntersectGenerated.setIntersectDependencies,
	  setSymDifferenceDependencies: _dependenciesSetSymDifferenceGenerated.setSymDifferenceDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  subsetDependencies: _dependenciesSubsetGenerated.subsetDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSetUnion: _factoriesAny.createSetUnion
	};
	return dependenciesSetUnion_generated;
}

var dependenciesSinh_generated = {};

var hasRequiredDependenciesSinh_generated;

function requireDependenciesSinh_generated () {
	if (hasRequiredDependenciesSinh_generated) return dependenciesSinh_generated;
	hasRequiredDependenciesSinh_generated = 1;

	Object.defineProperty(dependenciesSinh_generated, "__esModule", {
	  value: true
	});
	dependenciesSinh_generated.sinhDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSinh_generated.sinhDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSinh: _factoriesAny.createSinh
	};
	return dependenciesSinh_generated;
}

var dependenciesSolveODE_generated = {};

var hasRequiredDependenciesSolveODE_generated;

function requireDependenciesSolveODE_generated () {
	if (hasRequiredDependenciesSolveODE_generated) return dependenciesSolveODE_generated;
	hasRequiredDependenciesSolveODE_generated = 1;

	Object.defineProperty(dependenciesSolveODE_generated, "__esModule", {
	  value: true
	});
	dependenciesSolveODE_generated.solveODEDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
	var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesMaxGenerated = requireDependenciesMax_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSolveODE_generated.solveODEDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  bignumberDependencies: _dependenciesBignumberGenerated.bignumberDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  isPositiveDependencies: _dependenciesIsPositiveGenerated.isPositiveDependencies,
	  largerDependencies: _dependenciesLargerGenerated.largerDependencies,
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  maxDependencies: _dependenciesMaxGenerated.maxDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  smallerDependencies: _dependenciesSmallerGenerated.smallerDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  unaryMinusDependencies: _dependenciesUnaryMinusGenerated.unaryMinusDependencies,
	  createSolveODE: _factoriesAny.createSolveODE
	};
	return dependenciesSolveODE_generated;
}

var dependenciesSort_generated = {};

var hasRequiredDependenciesSort_generated;

function requireDependenciesSort_generated () {
	if (hasRequiredDependenciesSort_generated) return dependenciesSort_generated;
	hasRequiredDependenciesSort_generated = 1;

	Object.defineProperty(dependenciesSort_generated, "__esModule", {
	  value: true
	});
	dependenciesSort_generated.sortDependencies = void 0;
	var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
	var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSort_generated.sortDependencies = {
	  compareDependencies: _dependenciesCompareGenerated.compareDependencies,
	  compareNaturalDependencies: _dependenciesCompareNaturalGenerated.compareNaturalDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSort: _factoriesAny.createSort
	};
	return dependenciesSort_generated;
}

var dependenciesSparse_generated = {};

var hasRequiredDependenciesSparse_generated;

function requireDependenciesSparse_generated () {
	if (hasRequiredDependenciesSparse_generated) return dependenciesSparse_generated;
	hasRequiredDependenciesSparse_generated = 1;

	Object.defineProperty(dependenciesSparse_generated, "__esModule", {
	  value: true
	});
	dependenciesSparse_generated.sparseDependencies = void 0;
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSparse_generated.sparseDependencies = {
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSparse: _factoriesAny.createSparse
	};
	return dependenciesSparse_generated;
}

var dependenciesSpeedOfLight_generated = {};

var hasRequiredDependenciesSpeedOfLight_generated;

function requireDependenciesSpeedOfLight_generated () {
	if (hasRequiredDependenciesSpeedOfLight_generated) return dependenciesSpeedOfLight_generated;
	hasRequiredDependenciesSpeedOfLight_generated = 1;

	Object.defineProperty(dependenciesSpeedOfLight_generated, "__esModule", {
	  value: true
	});
	dependenciesSpeedOfLight_generated.speedOfLightDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSpeedOfLight_generated.speedOfLightDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createSpeedOfLight: _factoriesAny.createSpeedOfLight
	};
	return dependenciesSpeedOfLight_generated;
}

var dependenciesSplitUnit_generated = {};

var hasRequiredDependenciesSplitUnit_generated;

function requireDependenciesSplitUnit_generated () {
	if (hasRequiredDependenciesSplitUnit_generated) return dependenciesSplitUnit_generated;
	hasRequiredDependenciesSplitUnit_generated = 1;

	Object.defineProperty(dependenciesSplitUnit_generated, "__esModule", {
	  value: true
	});
	dependenciesSplitUnit_generated.splitUnitDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSplitUnit_generated.splitUnitDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSplitUnit: _factoriesAny.createSplitUnit
	};
	return dependenciesSplitUnit_generated;
}

var dependenciesSqrtm_generated = {};

var hasRequiredDependenciesSqrtm_generated;

function requireDependenciesSqrtm_generated () {
	if (hasRequiredDependenciesSqrtm_generated) return dependenciesSqrtm_generated;
	hasRequiredDependenciesSqrtm_generated = 1;

	Object.defineProperty(dependenciesSqrtm_generated, "__esModule", {
	  value: true
	});
	dependenciesSqrtm_generated.sqrtmDependencies = void 0;
	var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
	var _dependenciesInvGenerated = requireDependenciesInv_generated();
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesMaxGenerated = requireDependenciesMax_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSizeGenerated = requireDependenciesSize_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSqrtm_generated.sqrtmDependencies = {
	  absDependencies: _dependenciesAbsGenerated.absDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  identityDependencies: _dependenciesIdentityGenerated.identityDependencies,
	  invDependencies: _dependenciesInvGenerated.invDependencies,
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  maxDependencies: _dependenciesMaxGenerated.maxDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  sizeDependencies: _dependenciesSizeGenerated.sizeDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSqrtm: _factoriesAny.createSqrtm
	};
	return dependenciesSqrtm_generated;
}

var dependenciesSquare_generated = {};

var hasRequiredDependenciesSquare_generated;

function requireDependenciesSquare_generated () {
	if (hasRequiredDependenciesSquare_generated) return dependenciesSquare_generated;
	hasRequiredDependenciesSquare_generated = 1;

	Object.defineProperty(dependenciesSquare_generated, "__esModule", {
	  value: true
	});
	dependenciesSquare_generated.squareDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSquare_generated.squareDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSquare: _factoriesAny.createSquare
	};
	return dependenciesSquare_generated;
}

var dependenciesSqueeze_generated = {};

var hasRequiredDependenciesSqueeze_generated;

function requireDependenciesSqueeze_generated () {
	if (hasRequiredDependenciesSqueeze_generated) return dependenciesSqueeze_generated;
	hasRequiredDependenciesSqueeze_generated = 1;

	Object.defineProperty(dependenciesSqueeze_generated, "__esModule", {
	  value: true
	});
	dependenciesSqueeze_generated.squeezeDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSqueeze_generated.squeezeDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSqueeze: _factoriesAny.createSqueeze
	};
	return dependenciesSqueeze_generated;
}

var dependenciesStd_generated = {};

var dependenciesVariance_generated = {};

var hasRequiredDependenciesVariance_generated;

function requireDependenciesVariance_generated () {
	if (hasRequiredDependenciesVariance_generated) return dependenciesVariance_generated;
	hasRequiredDependenciesVariance_generated = 1;

	Object.defineProperty(dependenciesVariance_generated, "__esModule", {
	  value: true
	});
	dependenciesVariance_generated.varianceDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesMapSlicesGenerated = requireDependenciesMapSlices_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesVariance_generated.varianceDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  mapSlicesDependencies: _dependenciesMapSlicesGenerated.mapSlicesDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createVariance: _factoriesAny.createVariance
	};
	return dependenciesVariance_generated;
}

var hasRequiredDependenciesStd_generated;

function requireDependenciesStd_generated () {
	if (hasRequiredDependenciesStd_generated) return dependenciesStd_generated;
	hasRequiredDependenciesStd_generated = 1;

	Object.defineProperty(dependenciesStd_generated, "__esModule", {
	  value: true
	});
	dependenciesStd_generated.stdDependencies = void 0;
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesVarianceGenerated = requireDependenciesVariance_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesStd_generated.stdDependencies = {
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  varianceDependencies: _dependenciesVarianceGenerated.varianceDependencies,
	  createStd: _factoriesAny.createStd
	};
	return dependenciesStd_generated;
}

var dependenciesStdTransform_generated = {};

var hasRequiredDependenciesStdTransform_generated;

function requireDependenciesStdTransform_generated () {
	if (hasRequiredDependenciesStdTransform_generated) return dependenciesStdTransform_generated;
	hasRequiredDependenciesStdTransform_generated = 1;

	Object.defineProperty(dependenciesStdTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesStdTransform_generated.stdTransformDependencies = void 0;
	var _dependenciesMapGenerated = requireDependenciesMap_generated();
	var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesVarianceGenerated = requireDependenciesVariance_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesStdTransform_generated.stdTransformDependencies = {
	  mapDependencies: _dependenciesMapGenerated.mapDependencies,
	  sqrtDependencies: _dependenciesSqrtGenerated.sqrtDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  varianceDependencies: _dependenciesVarianceGenerated.varianceDependencies,
	  createStdTransform: _factoriesAny.createStdTransform
	};
	return dependenciesStdTransform_generated;
}

var dependenciesStefanBoltzmann_generated = {};

var hasRequiredDependenciesStefanBoltzmann_generated;

function requireDependenciesStefanBoltzmann_generated () {
	if (hasRequiredDependenciesStefanBoltzmann_generated) return dependenciesStefanBoltzmann_generated;
	hasRequiredDependenciesStefanBoltzmann_generated = 1;

	Object.defineProperty(dependenciesStefanBoltzmann_generated, "__esModule", {
	  value: true
	});
	dependenciesStefanBoltzmann_generated.stefanBoltzmannDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesStefanBoltzmann_generated.stefanBoltzmannDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createStefanBoltzmann: _factoriesAny.createStefanBoltzmann
	};
	return dependenciesStefanBoltzmann_generated;
}

var dependenciesString_generated = {};

var hasRequiredDependenciesString_generated;

function requireDependenciesString_generated () {
	if (hasRequiredDependenciesString_generated) return dependenciesString_generated;
	hasRequiredDependenciesString_generated = 1;

	Object.defineProperty(dependenciesString_generated, "__esModule", {
	  value: true
	});
	dependenciesString_generated.stringDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesString_generated.stringDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createString: _factoriesAny.createString
	};
	return dependenciesString_generated;
}

var dependenciesSubsetTransform_generated = {};

var hasRequiredDependenciesSubsetTransform_generated;

function requireDependenciesSubsetTransform_generated () {
	if (hasRequiredDependenciesSubsetTransform_generated) return dependenciesSubsetTransform_generated;
	hasRequiredDependenciesSubsetTransform_generated = 1;

	Object.defineProperty(dependenciesSubsetTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesSubsetTransform_generated.subsetTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSubsetTransform_generated.subsetTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  zerosDependencies: _dependenciesZerosGenerated.zerosDependencies,
	  createSubsetTransform: _factoriesAny.createSubsetTransform
	};
	return dependenciesSubsetTransform_generated;
}

var dependenciesSumTransform_generated = {};

var hasRequiredDependenciesSumTransform_generated;

function requireDependenciesSumTransform_generated () {
	if (hasRequiredDependenciesSumTransform_generated) return dependenciesSumTransform_generated;
	hasRequiredDependenciesSumTransform_generated = 1;

	Object.defineProperty(dependenciesSumTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesSumTransform_generated.sumTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSumTransform_generated.sumTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  numericDependencies: _dependenciesNumericGenerated.numericDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSumTransform: _factoriesAny.createSumTransform
	};
	return dependenciesSumTransform_generated;
}

var dependenciesSymbolicEqual_generated = {};

var hasRequiredDependenciesSymbolicEqual_generated;

function requireDependenciesSymbolicEqual_generated () {
	if (hasRequiredDependenciesSymbolicEqual_generated) return dependenciesSymbolicEqual_generated;
	hasRequiredDependenciesSymbolicEqual_generated = 1;

	Object.defineProperty(dependenciesSymbolicEqual_generated, "__esModule", {
	  value: true
	});
	dependenciesSymbolicEqual_generated.symbolicEqualDependencies = void 0;
	var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
	var _dependenciesParseGenerated = requireDependenciesParse_generated();
	var _dependenciesSimplifyGenerated = requireDependenciesSimplify_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesSymbolicEqual_generated.symbolicEqualDependencies = {
	  OperatorNodeDependencies: _dependenciesOperatorNodeGenerated.OperatorNodeDependencies,
	  parseDependencies: _dependenciesParseGenerated.parseDependencies,
	  simplifyDependencies: _dependenciesSimplifyGenerated.simplifyDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createSymbolicEqual: _factoriesAny.createSymbolicEqual
	};
	return dependenciesSymbolicEqual_generated;
}

var dependenciesTan_generated = {};

var hasRequiredDependenciesTan_generated;

function requireDependenciesTan_generated () {
	if (hasRequiredDependenciesTan_generated) return dependenciesTan_generated;
	hasRequiredDependenciesTan_generated = 1;

	Object.defineProperty(dependenciesTan_generated, "__esModule", {
	  value: true
	});
	dependenciesTan_generated.tanDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTan_generated.tanDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createTan: _factoriesAny.createTan
	};
	return dependenciesTan_generated;
}

var dependenciesTanh_generated = {};

var hasRequiredDependenciesTanh_generated;

function requireDependenciesTanh_generated () {
	if (hasRequiredDependenciesTanh_generated) return dependenciesTanh_generated;
	hasRequiredDependenciesTanh_generated = 1;

	Object.defineProperty(dependenciesTanh_generated, "__esModule", {
	  value: true
	});
	dependenciesTanh_generated.tanhDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTanh_generated.tanhDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createTanh: _factoriesAny.createTanh
	};
	return dependenciesTanh_generated;
}

var dependenciesThomsonCrossSection_generated = {};

var hasRequiredDependenciesThomsonCrossSection_generated;

function requireDependenciesThomsonCrossSection_generated () {
	if (hasRequiredDependenciesThomsonCrossSection_generated) return dependenciesThomsonCrossSection_generated;
	hasRequiredDependenciesThomsonCrossSection_generated = 1;

	Object.defineProperty(dependenciesThomsonCrossSection_generated, "__esModule", {
	  value: true
	});
	dependenciesThomsonCrossSection_generated.thomsonCrossSectionDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesThomsonCrossSection_generated.thomsonCrossSectionDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createThomsonCrossSection: _factoriesAny.createThomsonCrossSection
	};
	return dependenciesThomsonCrossSection_generated;
}

var dependenciesTo_generated = {};

var hasRequiredDependenciesTo_generated;

function requireDependenciesTo_generated () {
	if (hasRequiredDependenciesTo_generated) return dependenciesTo_generated;
	hasRequiredDependenciesTo_generated = 1;

	Object.defineProperty(dependenciesTo_generated, "__esModule", {
	  value: true
	});
	dependenciesTo_generated.toDependencies = void 0;
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTo_generated.toDependencies = {
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createTo: _factoriesAny.createTo
	};
	return dependenciesTo_generated;
}

var dependenciesToBest_generated = {};

var hasRequiredDependenciesToBest_generated;

function requireDependenciesToBest_generated () {
	if (hasRequiredDependenciesToBest_generated) return dependenciesToBest_generated;
	hasRequiredDependenciesToBest_generated = 1;

	Object.defineProperty(dependenciesToBest_generated, "__esModule", {
	  value: true
	});
	dependenciesToBest_generated.toBestDependencies = void 0;
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesToBest_generated.toBestDependencies = {
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createToBest: _factoriesAny.createToBest
	};
	return dependenciesToBest_generated;
}

var dependenciesTrace_generated = {};

var hasRequiredDependenciesTrace_generated;

function requireDependenciesTrace_generated () {
	if (hasRequiredDependenciesTrace_generated) return dependenciesTrace_generated;
	hasRequiredDependenciesTrace_generated = 1;

	Object.defineProperty(dependenciesTrace_generated, "__esModule", {
	  value: true
	});
	dependenciesTrace_generated.traceDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTrace_generated.traceDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createTrace: _factoriesAny.createTrace
	};
	return dependenciesTrace_generated;
}

var dependenciesTrue_generated = {};

var hasRequiredDependenciesTrue_generated;

function requireDependenciesTrue_generated () {
	if (hasRequiredDependenciesTrue_generated) return dependenciesTrue_generated;
	hasRequiredDependenciesTrue_generated = 1;

	Object.defineProperty(dependenciesTrue_generated, "__esModule", {
	  value: true
	});
	dependenciesTrue_generated.trueDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesTrue_generated.trueDependencies = {
	  createTrue: _factoriesAny.createTrue
	};
	return dependenciesTrue_generated;
}

var dependenciesUnequal_generated = {};

var hasRequiredDependenciesUnequal_generated;

function requireDependenciesUnequal_generated () {
	if (hasRequiredDependenciesUnequal_generated) return dependenciesUnequal_generated;
	hasRequiredDependenciesUnequal_generated = 1;

	Object.defineProperty(dependenciesUnequal_generated, "__esModule", {
	  value: true
	});
	dependenciesUnequal_generated.unequalDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUnequal_generated.unequalDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  equalScalarDependencies: _dependenciesEqualScalarGenerated.equalScalarDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createUnequal: _factoriesAny.createUnequal
	};
	return dependenciesUnequal_generated;
}

var dependenciesUnitFunction_generated = {};

var hasRequiredDependenciesUnitFunction_generated;

function requireDependenciesUnitFunction_generated () {
	if (hasRequiredDependenciesUnitFunction_generated) return dependenciesUnitFunction_generated;
	hasRequiredDependenciesUnitFunction_generated = 1;

	Object.defineProperty(dependenciesUnitFunction_generated, "__esModule", {
	  value: true
	});
	dependenciesUnitFunction_generated.unitDependencies = void 0;
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUnitFunction_generated.unitDependencies = {
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createUnitFunction: _factoriesAny.createUnitFunction
	};
	return dependenciesUnitFunction_generated;
}

var dependenciesUppercaseE_generated = {};

var hasRequiredDependenciesUppercaseE_generated;

function requireDependenciesUppercaseE_generated () {
	if (hasRequiredDependenciesUppercaseE_generated) return dependenciesUppercaseE_generated;
	hasRequiredDependenciesUppercaseE_generated = 1;

	Object.defineProperty(dependenciesUppercaseE_generated, "__esModule", {
	  value: true
	});
	dependenciesUppercaseE_generated.EDependencies = void 0;
	var _dependenciesEGenerated = requireDependenciesE_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUppercaseE_generated.EDependencies = {
	  eDependencies: _dependenciesEGenerated.eDependencies,
	  createUppercaseE: _factoriesAny.createUppercaseE
	};
	return dependenciesUppercaseE_generated;
}

var dependenciesUppercasePi_generated = {};

var hasRequiredDependenciesUppercasePi_generated;

function requireDependenciesUppercasePi_generated () {
	if (hasRequiredDependenciesUppercasePi_generated) return dependenciesUppercasePi_generated;
	hasRequiredDependenciesUppercasePi_generated = 1;

	Object.defineProperty(dependenciesUppercasePi_generated, "__esModule", {
	  value: true
	});
	dependenciesUppercasePi_generated.PIDependencies = void 0;
	var _dependenciesPiGenerated = requireDependenciesPi_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesUppercasePi_generated.PIDependencies = {
	  piDependencies: _dependenciesPiGenerated.piDependencies,
	  createUppercasePi: _factoriesAny.createUppercasePi
	};
	return dependenciesUppercasePi_generated;
}

var dependenciesVacuumImpedance_generated = {};

var hasRequiredDependenciesVacuumImpedance_generated;

function requireDependenciesVacuumImpedance_generated () {
	if (hasRequiredDependenciesVacuumImpedance_generated) return dependenciesVacuumImpedance_generated;
	hasRequiredDependenciesVacuumImpedance_generated = 1;

	Object.defineProperty(dependenciesVacuumImpedance_generated, "__esModule", {
	  value: true
	});
	dependenciesVacuumImpedance_generated.vacuumImpedanceDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesVacuumImpedance_generated.vacuumImpedanceDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createVacuumImpedance: _factoriesAny.createVacuumImpedance
	};
	return dependenciesVacuumImpedance_generated;
}

var dependenciesVarianceTransform_generated = {};

var hasRequiredDependenciesVarianceTransform_generated;

function requireDependenciesVarianceTransform_generated () {
	if (hasRequiredDependenciesVarianceTransform_generated) return dependenciesVarianceTransform_generated;
	hasRequiredDependenciesVarianceTransform_generated = 1;

	Object.defineProperty(dependenciesVarianceTransform_generated, "__esModule", {
	  value: true
	});
	dependenciesVarianceTransform_generated.varianceTransformDependencies = void 0;
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
	var _dependenciesMapSlicesGenerated = requireDependenciesMapSlices_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesVarianceTransform_generated.varianceTransformDependencies = {
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  isNaNDependencies: _dependenciesIsNaNGenerated.isNaNDependencies,
	  mapSlicesDependencies: _dependenciesMapSlicesGenerated.mapSlicesDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createVarianceTransform: _factoriesAny.createVarianceTransform
	};
	return dependenciesVarianceTransform_generated;
}

var dependenciesVersion_generated = {};

var hasRequiredDependenciesVersion_generated;

function requireDependenciesVersion_generated () {
	if (hasRequiredDependenciesVersion_generated) return dependenciesVersion_generated;
	hasRequiredDependenciesVersion_generated = 1;

	Object.defineProperty(dependenciesVersion_generated, "__esModule", {
	  value: true
	});
	dependenciesVersion_generated.versionDependencies = void 0;
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesVersion_generated.versionDependencies = {
	  createVersion: _factoriesAny.createVersion
	};
	return dependenciesVersion_generated;
}

var dependenciesWeakMixingAngle_generated = {};

var hasRequiredDependenciesWeakMixingAngle_generated;

function requireDependenciesWeakMixingAngle_generated () {
	if (hasRequiredDependenciesWeakMixingAngle_generated) return dependenciesWeakMixingAngle_generated;
	hasRequiredDependenciesWeakMixingAngle_generated = 1;

	Object.defineProperty(dependenciesWeakMixingAngle_generated, "__esModule", {
	  value: true
	});
	dependenciesWeakMixingAngle_generated.weakMixingAngleDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesWeakMixingAngle_generated.weakMixingAngleDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  createWeakMixingAngle: _factoriesAny.createWeakMixingAngle
	};
	return dependenciesWeakMixingAngle_generated;
}

var dependenciesWienDisplacement_generated = {};

var hasRequiredDependenciesWienDisplacement_generated;

function requireDependenciesWienDisplacement_generated () {
	if (hasRequiredDependenciesWienDisplacement_generated) return dependenciesWienDisplacement_generated;
	hasRequiredDependenciesWienDisplacement_generated = 1;

	Object.defineProperty(dependenciesWienDisplacement_generated, "__esModule", {
	  value: true
	});
	dependenciesWienDisplacement_generated.wienDisplacementDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesWienDisplacement_generated.wienDisplacementDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  UnitDependencies: _dependenciesUnitClassGenerated.UnitDependencies,
	  createWienDisplacement: _factoriesAny.createWienDisplacement
	};
	return dependenciesWienDisplacement_generated;
}

var dependenciesXor_generated = {};

var hasRequiredDependenciesXor_generated;

function requireDependenciesXor_generated () {
	if (hasRequiredDependenciesXor_generated) return dependenciesXor_generated;
	hasRequiredDependenciesXor_generated = 1;

	Object.defineProperty(dependenciesXor_generated, "__esModule", {
	  value: true
	});
	dependenciesXor_generated.xorDependencies = void 0;
	var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
	var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
	var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
	var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesXor_generated.xorDependencies = {
	  DenseMatrixDependencies: _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies,
	  SparseMatrixDependencies: _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies,
	  concatDependencies: _dependenciesConcatGenerated.concatDependencies,
	  matrixDependencies: _dependenciesMatrixGenerated.matrixDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createXor: _factoriesAny.createXor
	};
	return dependenciesXor_generated;
}

var dependenciesZeta_generated = {};

var hasRequiredDependenciesZeta_generated;

function requireDependenciesZeta_generated () {
	if (hasRequiredDependenciesZeta_generated) return dependenciesZeta_generated;
	hasRequiredDependenciesZeta_generated = 1;

	Object.defineProperty(dependenciesZeta_generated, "__esModule", {
	  value: true
	});
	dependenciesZeta_generated.zetaDependencies = void 0;
	var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
	var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
	var _dependenciesFactorialGenerated = requireDependenciesFactorial_generated();
	var _dependenciesGammaGenerated = requireDependenciesGamma_generated();
	var _dependenciesIsBoundedGenerated = requireDependenciesIsBounded_generated();
	var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesPiGenerated = requireDependenciesPi_generated();
	var _dependenciesPowGenerated = requireDependenciesPow_generated();
	var _dependenciesSinGenerated = requireDependenciesSin_generated();
	var _dependenciesSmallerEqGenerated = requireDependenciesSmallerEq_generated();
	var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesZeta_generated.zetaDependencies = {
	  BigNumberDependencies: _dependenciesBigNumberClassGenerated.BigNumberDependencies,
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  divideDependencies: _dependenciesDivideGenerated.divideDependencies,
	  equalDependencies: _dependenciesEqualGenerated.equalDependencies,
	  factorialDependencies: _dependenciesFactorialGenerated.factorialDependencies,
	  gammaDependencies: _dependenciesGammaGenerated.gammaDependencies,
	  isBoundedDependencies: _dependenciesIsBoundedGenerated.isBoundedDependencies,
	  isNegativeDependencies: _dependenciesIsNegativeGenerated.isNegativeDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  piDependencies: _dependenciesPiGenerated.piDependencies,
	  powDependencies: _dependenciesPowGenerated.powDependencies,
	  sinDependencies: _dependenciesSinGenerated.sinDependencies,
	  smallerEqDependencies: _dependenciesSmallerEqGenerated.smallerEqDependencies,
	  subtractDependencies: _dependenciesSubtractGenerated.subtractDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createZeta: _factoriesAny.createZeta
	};
	return dependenciesZeta_generated;
}

var dependenciesZpk2tf_generated = {};

var hasRequiredDependenciesZpk2tf_generated;

function requireDependenciesZpk2tf_generated () {
	if (hasRequiredDependenciesZpk2tf_generated) return dependenciesZpk2tf_generated;
	hasRequiredDependenciesZpk2tf_generated = 1;

	Object.defineProperty(dependenciesZpk2tf_generated, "__esModule", {
	  value: true
	});
	dependenciesZpk2tf_generated.zpk2tfDependencies = void 0;
	var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
	var _dependenciesAddGenerated = requireDependenciesAdd_generated();
	var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
	var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
	var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
	var _factoriesAny = requireFactoriesAny();
	/**
	 * THIS FILE IS AUTO-GENERATED
	 * DON'T MAKE CHANGES HERE
	 */

	dependenciesZpk2tf_generated.zpk2tfDependencies = {
	  ComplexDependencies: _dependenciesComplexClassGenerated.ComplexDependencies,
	  addDependencies: _dependenciesAddGenerated.addDependencies,
	  multiplyDependencies: _dependenciesMultiplyGenerated.multiplyDependencies,
	  numberDependencies: _dependenciesNumberGenerated.numberDependencies,
	  typedDependencies: _dependenciesTypedGenerated.typedDependencies,
	  createZpk2tf: _factoriesAny.createZpk2tf
	};
	return dependenciesZpk2tf_generated;
}

var allFactoriesAny = {};

var hasRequiredAllFactoriesAny;

function requireAllFactoriesAny () {
	if (hasRequiredAllFactoriesAny) return allFactoriesAny;
	hasRequiredAllFactoriesAny = 1;

	Object.defineProperty(allFactoriesAny, "__esModule", {
	  value: true
	});
	allFactoriesAny.all = void 0;
	var allFactories = _interopRequireWildcard(requireFactoriesAny());
	function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
	// creating all factories here in a separate file is needed to get tree-shaking working

	allFactoriesAny.all = allFactories;
	return allFactoriesAny;
}

var hasRequiredDependenciesAny_generated;

function requireDependenciesAny_generated () {
	if (hasRequiredDependenciesAny_generated) return dependenciesAny_generated;
	hasRequiredDependenciesAny_generated = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		Object.defineProperty(exports, "AccessorNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAccessorNodeGenerated.AccessorNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "ArrayNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesArrayNodeGenerated.ArrayNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "AssignmentNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAssignmentNodeGenerated.AssignmentNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "BigNumberDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBigNumberClassGenerated.BigNumberDependencies;
		  }
		});
		Object.defineProperty(exports, "BlockNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBlockNodeGenerated.BlockNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "ChainDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesChainClassGenerated.ChainDependencies;
		  }
		});
		Object.defineProperty(exports, "ComplexDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesComplexClassGenerated.ComplexDependencies;
		  }
		});
		Object.defineProperty(exports, "ConditionalNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesConditionalNodeGenerated.ConditionalNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "ConstantNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesConstantNodeGenerated.ConstantNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "DenseMatrixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDenseMatrixClassGenerated.DenseMatrixDependencies;
		  }
		});
		Object.defineProperty(exports, "EDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUppercaseEGenerated.EDependencies;
		  }
		});
		Object.defineProperty(exports, "FibonacciHeapDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFibonacciHeapClassGenerated.FibonacciHeapDependencies;
		  }
		});
		Object.defineProperty(exports, "FractionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFractionClassGenerated.FractionDependencies;
		  }
		});
		Object.defineProperty(exports, "FunctionAssignmentNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFunctionAssignmentNodeGenerated.FunctionAssignmentNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "FunctionNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFunctionNodeGenerated.FunctionNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "HelpDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesHelpClassGenerated.HelpDependencies;
		  }
		});
		Object.defineProperty(exports, "ImmutableDenseMatrixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesImmutableDenseMatrixClassGenerated.ImmutableDenseMatrixDependencies;
		  }
		});
		Object.defineProperty(exports, "IndexDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIndexClassGenerated.IndexDependencies;
		  }
		});
		Object.defineProperty(exports, "IndexNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIndexNodeGenerated.IndexNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "InfinityDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesInfinityGenerated.InfinityDependencies;
		  }
		});
		Object.defineProperty(exports, "LN10Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLN10Generated.LN10Dependencies;
		  }
		});
		Object.defineProperty(exports, "LN2Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLN2Generated.LN2Dependencies;
		  }
		});
		Object.defineProperty(exports, "LOG10EDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLOG10EGenerated.LOG10EDependencies;
		  }
		});
		Object.defineProperty(exports, "LOG2EDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLOG2EGenerated.LOG2EDependencies;
		  }
		});
		Object.defineProperty(exports, "MatrixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMatrixClassGenerated.MatrixDependencies;
		  }
		});
		Object.defineProperty(exports, "NaNDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNaNGenerated.NaNDependencies;
		  }
		});
		Object.defineProperty(exports, "NodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNodeGenerated.NodeDependencies;
		  }
		});
		Object.defineProperty(exports, "ObjectNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesObjectNodeGenerated.ObjectNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "OperatorNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesOperatorNodeGenerated.OperatorNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "PIDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUppercasePiGenerated.PIDependencies;
		  }
		});
		Object.defineProperty(exports, "ParenthesisNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesParenthesisNodeGenerated.ParenthesisNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "ParserDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesParserClassGenerated.ParserDependencies;
		  }
		});
		Object.defineProperty(exports, "RangeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRangeClassGenerated.RangeDependencies;
		  }
		});
		Object.defineProperty(exports, "RangeNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRangeNodeGenerated.RangeNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "RelationalNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRelationalNodeGenerated.RelationalNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "ResultSetDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesResultSetGenerated.ResultSetDependencies;
		  }
		});
		Object.defineProperty(exports, "SQRT1_2Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSQRT1_2Generated.SQRT1_2Dependencies;
		  }
		});
		Object.defineProperty(exports, "SQRT2Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSQRT2Generated.SQRT2Dependencies;
		  }
		});
		Object.defineProperty(exports, "SpaDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSpaClassGenerated.SpaDependencies;
		  }
		});
		Object.defineProperty(exports, "SparseMatrixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSparseMatrixClassGenerated.SparseMatrixDependencies;
		  }
		});
		Object.defineProperty(exports, "SymbolNodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSymbolNodeGenerated.SymbolNodeDependencies;
		  }
		});
		Object.defineProperty(exports, "UnitDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUnitClassGenerated.UnitDependencies;
		  }
		});
		Object.defineProperty(exports, "absDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAbsGenerated.absDependencies;
		  }
		});
		Object.defineProperty(exports, "acosDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAcosGenerated.acosDependencies;
		  }
		});
		Object.defineProperty(exports, "acoshDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAcoshGenerated.acoshDependencies;
		  }
		});
		Object.defineProperty(exports, "acotDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAcotGenerated.acotDependencies;
		  }
		});
		Object.defineProperty(exports, "acothDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAcothGenerated.acothDependencies;
		  }
		});
		Object.defineProperty(exports, "acscDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAcscGenerated.acscDependencies;
		  }
		});
		Object.defineProperty(exports, "acschDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAcschGenerated.acschDependencies;
		  }
		});
		Object.defineProperty(exports, "addDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAddGenerated.addDependencies;
		  }
		});
		Object.defineProperty(exports, "addScalarDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAddScalarGenerated.addScalarDependencies;
		  }
		});
		Object.defineProperty(exports, "all", {
		  enumerable: true,
		  get: function () {
		    return _allFactoriesAny.all;
		  }
		});
		Object.defineProperty(exports, "andDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAndGenerated.andDependencies;
		  }
		});
		Object.defineProperty(exports, "andTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAndTransformGenerated.andTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "argDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesArgGenerated.argDependencies;
		  }
		});
		Object.defineProperty(exports, "asecDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAsecGenerated.asecDependencies;
		  }
		});
		Object.defineProperty(exports, "asechDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAsechGenerated.asechDependencies;
		  }
		});
		Object.defineProperty(exports, "asinDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAsinGenerated.asinDependencies;
		  }
		});
		Object.defineProperty(exports, "asinhDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAsinhGenerated.asinhDependencies;
		  }
		});
		Object.defineProperty(exports, "atan2Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAtan2Generated.atan2Dependencies;
		  }
		});
		Object.defineProperty(exports, "atanDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAtanGenerated.atanDependencies;
		  }
		});
		Object.defineProperty(exports, "atanhDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAtanhGenerated.atanhDependencies;
		  }
		});
		Object.defineProperty(exports, "atomicMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAtomicMassGenerated.atomicMassDependencies;
		  }
		});
		Object.defineProperty(exports, "avogadroDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesAvogadroGenerated.avogadroDependencies;
		  }
		});
		Object.defineProperty(exports, "bellNumbersDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBellNumbersGenerated.bellNumbersDependencies;
		  }
		});
		Object.defineProperty(exports, "bernoulliDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBernoulliGenerated.bernoulliDependencies;
		  }
		});
		Object.defineProperty(exports, "bigintDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBigintGenerated.bigintDependencies;
		  }
		});
		Object.defineProperty(exports, "bignumberDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBignumberGenerated.bignumberDependencies;
		  }
		});
		Object.defineProperty(exports, "binDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBinGenerated.binDependencies;
		  }
		});
		Object.defineProperty(exports, "bitAndDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBitAndGenerated.bitAndDependencies;
		  }
		});
		Object.defineProperty(exports, "bitAndTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBitAndTransformGenerated.bitAndTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "bitNotDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBitNotGenerated.bitNotDependencies;
		  }
		});
		Object.defineProperty(exports, "bitOrDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBitOrGenerated.bitOrDependencies;
		  }
		});
		Object.defineProperty(exports, "bitOrTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBitOrTransformGenerated.bitOrTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "bitXorDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBitXorGenerated.bitXorDependencies;
		  }
		});
		Object.defineProperty(exports, "bohrMagnetonDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBohrMagnetonGenerated.bohrMagnetonDependencies;
		  }
		});
		Object.defineProperty(exports, "bohrRadiusDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBohrRadiusGenerated.bohrRadiusDependencies;
		  }
		});
		Object.defineProperty(exports, "boltzmannDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBoltzmannGenerated.boltzmannDependencies;
		  }
		});
		Object.defineProperty(exports, "booleanDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesBooleanGenerated.booleanDependencies;
		  }
		});
		Object.defineProperty(exports, "catalanDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCatalanGenerated.catalanDependencies;
		  }
		});
		Object.defineProperty(exports, "cbrtDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCbrtGenerated.cbrtDependencies;
		  }
		});
		Object.defineProperty(exports, "ceilDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCeilGenerated.ceilDependencies;
		  }
		});
		Object.defineProperty(exports, "chainDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesChainGenerated.chainDependencies;
		  }
		});
		Object.defineProperty(exports, "classicalElectronRadiusDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesClassicalElectronRadiusGenerated.classicalElectronRadiusDependencies;
		  }
		});
		Object.defineProperty(exports, "cloneDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCloneGenerated.cloneDependencies;
		  }
		});
		Object.defineProperty(exports, "columnDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesColumnGenerated.columnDependencies;
		  }
		});
		Object.defineProperty(exports, "columnTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesColumnTransformGenerated.columnTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "combinationsDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCombinationsGenerated.combinationsDependencies;
		  }
		});
		Object.defineProperty(exports, "combinationsWithRepDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCombinationsWithRepGenerated.combinationsWithRepDependencies;
		  }
		});
		Object.defineProperty(exports, "compareDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCompareGenerated.compareDependencies;
		  }
		});
		Object.defineProperty(exports, "compareNaturalDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCompareNaturalGenerated.compareNaturalDependencies;
		  }
		});
		Object.defineProperty(exports, "compareTextDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCompareTextGenerated.compareTextDependencies;
		  }
		});
		Object.defineProperty(exports, "compileDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCompileGenerated.compileDependencies;
		  }
		});
		Object.defineProperty(exports, "complexDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesComplexGenerated.complexDependencies;
		  }
		});
		Object.defineProperty(exports, "compositionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCompositionGenerated.compositionDependencies;
		  }
		});
		Object.defineProperty(exports, "concatDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesConcatGenerated.concatDependencies;
		  }
		});
		Object.defineProperty(exports, "concatTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesConcatTransformGenerated.concatTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "conductanceQuantumDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesConductanceQuantumGenerated.conductanceQuantumDependencies;
		  }
		});
		Object.defineProperty(exports, "conjDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesConjGenerated.conjDependencies;
		  }
		});
		Object.defineProperty(exports, "corrDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCorrGenerated.corrDependencies;
		  }
		});
		Object.defineProperty(exports, "cosDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCosGenerated.cosDependencies;
		  }
		});
		Object.defineProperty(exports, "coshDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCoshGenerated.coshDependencies;
		  }
		});
		Object.defineProperty(exports, "cotDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCotGenerated.cotDependencies;
		  }
		});
		Object.defineProperty(exports, "cothDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCothGenerated.cothDependencies;
		  }
		});
		Object.defineProperty(exports, "coulombConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCoulombConstantGenerated.coulombConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "coulombDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCoulombGenerated.coulombDependencies;
		  }
		});
		Object.defineProperty(exports, "countDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCountGenerated.countDependencies;
		  }
		});
		Object.defineProperty(exports, "createUnitDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCreateUnitGenerated.createUnitDependencies;
		  }
		});
		Object.defineProperty(exports, "crossDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCrossGenerated.crossDependencies;
		  }
		});
		Object.defineProperty(exports, "cscDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCscGenerated.cscDependencies;
		  }
		});
		Object.defineProperty(exports, "cschDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCschGenerated.cschDependencies;
		  }
		});
		Object.defineProperty(exports, "ctransposeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCtransposeGenerated.ctransposeDependencies;
		  }
		});
		Object.defineProperty(exports, "cubeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCubeGenerated.cubeDependencies;
		  }
		});
		Object.defineProperty(exports, "cumsumDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCumSumGenerated.cumsumDependencies;
		  }
		});
		Object.defineProperty(exports, "cumsumTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesCumSumTransformGenerated.cumsumTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "deepEqualDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDeepEqualGenerated.deepEqualDependencies;
		  }
		});
		Object.defineProperty(exports, "denDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDenGenerated.denDependencies;
		  }
		});
		Object.defineProperty(exports, "derivativeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDerivativeGenerated.derivativeDependencies;
		  }
		});
		Object.defineProperty(exports, "detDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDetGenerated.detDependencies;
		  }
		});
		Object.defineProperty(exports, "deuteronMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDeuteronMassGenerated.deuteronMassDependencies;
		  }
		});
		Object.defineProperty(exports, "diagDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDiagGenerated.diagDependencies;
		  }
		});
		Object.defineProperty(exports, "diffDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDiffGenerated.diffDependencies;
		  }
		});
		Object.defineProperty(exports, "diffTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDiffTransformGenerated.diffTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "distanceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDistanceGenerated.distanceDependencies;
		  }
		});
		Object.defineProperty(exports, "divideDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDivideGenerated.divideDependencies;
		  }
		});
		Object.defineProperty(exports, "divideScalarDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDivideScalarGenerated.divideScalarDependencies;
		  }
		});
		Object.defineProperty(exports, "dotDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDotGenerated.dotDependencies;
		  }
		});
		Object.defineProperty(exports, "dotDivideDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDotDivideGenerated.dotDivideDependencies;
		  }
		});
		Object.defineProperty(exports, "dotMultiplyDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDotMultiplyGenerated.dotMultiplyDependencies;
		  }
		});
		Object.defineProperty(exports, "dotPowDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesDotPowGenerated.dotPowDependencies;
		  }
		});
		Object.defineProperty(exports, "eDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEGenerated.eDependencies;
		  }
		});
		Object.defineProperty(exports, "efimovFactorDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEfimovFactorGenerated.efimovFactorDependencies;
		  }
		});
		Object.defineProperty(exports, "eigsDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEigsGenerated.eigsDependencies;
		  }
		});
		Object.defineProperty(exports, "electricConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesElectricConstantGenerated.electricConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "electronMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesElectronMassGenerated.electronMassDependencies;
		  }
		});
		Object.defineProperty(exports, "elementaryChargeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesElementaryChargeGenerated.elementaryChargeDependencies;
		  }
		});
		Object.defineProperty(exports, "equalDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEqualGenerated.equalDependencies;
		  }
		});
		Object.defineProperty(exports, "equalScalarDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEqualScalarGenerated.equalScalarDependencies;
		  }
		});
		Object.defineProperty(exports, "equalTextDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEqualTextGenerated.equalTextDependencies;
		  }
		});
		Object.defineProperty(exports, "erfDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesErfGenerated.erfDependencies;
		  }
		});
		Object.defineProperty(exports, "evaluateDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesEvaluateGenerated.evaluateDependencies;
		  }
		});
		Object.defineProperty(exports, "expDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesExpGenerated.expDependencies;
		  }
		});
		Object.defineProperty(exports, "expm1Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesExpm1Generated.expm1Dependencies;
		  }
		});
		Object.defineProperty(exports, "expmDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesExpmGenerated.expmDependencies;
		  }
		});
		Object.defineProperty(exports, "factorialDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFactorialGenerated.factorialDependencies;
		  }
		});
		Object.defineProperty(exports, "falseDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFalseGenerated.falseDependencies;
		  }
		});
		Object.defineProperty(exports, "faradayDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFaradayGenerated.faradayDependencies;
		  }
		});
		Object.defineProperty(exports, "fermiCouplingDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFermiCouplingGenerated.fermiCouplingDependencies;
		  }
		});
		Object.defineProperty(exports, "fftDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFftGenerated.fftDependencies;
		  }
		});
		Object.defineProperty(exports, "filterDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFilterGenerated.filterDependencies;
		  }
		});
		Object.defineProperty(exports, "filterTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFilterTransformGenerated.filterTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "fineStructureDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFineStructureGenerated.fineStructureDependencies;
		  }
		});
		Object.defineProperty(exports, "firstRadiationDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFirstRadiationGenerated.firstRadiationDependencies;
		  }
		});
		Object.defineProperty(exports, "fixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFixGenerated.fixDependencies;
		  }
		});
		Object.defineProperty(exports, "flattenDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFlattenGenerated.flattenDependencies;
		  }
		});
		Object.defineProperty(exports, "floorDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFloorGenerated.floorDependencies;
		  }
		});
		Object.defineProperty(exports, "forEachDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesForEachGenerated.forEachDependencies;
		  }
		});
		Object.defineProperty(exports, "forEachTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesForEachTransformGenerated.forEachTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "formatDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFormatGenerated.formatDependencies;
		  }
		});
		Object.defineProperty(exports, "fractionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFractionGenerated.fractionDependencies;
		  }
		});
		Object.defineProperty(exports, "freqzDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesFreqzGenerated.freqzDependencies;
		  }
		});
		Object.defineProperty(exports, "gammaDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesGammaGenerated.gammaDependencies;
		  }
		});
		Object.defineProperty(exports, "gasConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesGasConstantGenerated.gasConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "gcdDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesGcdGenerated.gcdDependencies;
		  }
		});
		Object.defineProperty(exports, "getMatrixDataTypeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesGetMatrixDataTypeGenerated.getMatrixDataTypeDependencies;
		  }
		});
		Object.defineProperty(exports, "gravitationConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesGravitationConstantGenerated.gravitationConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "gravityDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesGravityGenerated.gravityDependencies;
		  }
		});
		Object.defineProperty(exports, "hartreeEnergyDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesHartreeEnergyGenerated.hartreeEnergyDependencies;
		  }
		});
		Object.defineProperty(exports, "hasNumericValueDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesHasNumericValueGenerated.hasNumericValueDependencies;
		  }
		});
		Object.defineProperty(exports, "helpDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesHelpGenerated.helpDependencies;
		  }
		});
		Object.defineProperty(exports, "hexDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesHexGenerated.hexDependencies;
		  }
		});
		Object.defineProperty(exports, "hypotDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesHypotGenerated.hypotDependencies;
		  }
		});
		Object.defineProperty(exports, "iDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIGenerated.iDependencies;
		  }
		});
		Object.defineProperty(exports, "identityDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIdentityGenerated.identityDependencies;
		  }
		});
		Object.defineProperty(exports, "ifftDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIfftGenerated.ifftDependencies;
		  }
		});
		Object.defineProperty(exports, "imDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesImGenerated.imDependencies;
		  }
		});
		Object.defineProperty(exports, "indexDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIndexGenerated.indexDependencies;
		  }
		});
		Object.defineProperty(exports, "indexTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIndexTransformGenerated.indexTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "intersectDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIntersectGenerated.intersectDependencies;
		  }
		});
		Object.defineProperty(exports, "invDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesInvGenerated.invDependencies;
		  }
		});
		Object.defineProperty(exports, "inverseConductanceQuantumDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesInverseConductanceQuantumGenerated.inverseConductanceQuantumDependencies;
		  }
		});
		Object.defineProperty(exports, "invmodDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesInvmodGenerated.invmodDependencies;
		  }
		});
		Object.defineProperty(exports, "isBoundedDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsBoundedGenerated.isBoundedDependencies;
		  }
		});
		Object.defineProperty(exports, "isFiniteDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsFiniteGenerated.isFiniteDependencies;
		  }
		});
		Object.defineProperty(exports, "isIntegerDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsIntegerGenerated.isIntegerDependencies;
		  }
		});
		Object.defineProperty(exports, "isNaNDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsNaNGenerated.isNaNDependencies;
		  }
		});
		Object.defineProperty(exports, "isNegativeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsNegativeGenerated.isNegativeDependencies;
		  }
		});
		Object.defineProperty(exports, "isNumericDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsNumericGenerated.isNumericDependencies;
		  }
		});
		Object.defineProperty(exports, "isPositiveDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsPositiveGenerated.isPositiveDependencies;
		  }
		});
		Object.defineProperty(exports, "isPrimeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsPrimeGenerated.isPrimeDependencies;
		  }
		});
		Object.defineProperty(exports, "isZeroDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesIsZeroGenerated.isZeroDependencies;
		  }
		});
		Object.defineProperty(exports, "kldivergenceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesKldivergenceGenerated.kldivergenceDependencies;
		  }
		});
		Object.defineProperty(exports, "klitzingDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesKlitzingGenerated.klitzingDependencies;
		  }
		});
		Object.defineProperty(exports, "kronDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesKronGenerated.kronDependencies;
		  }
		});
		Object.defineProperty(exports, "largerDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLargerGenerated.largerDependencies;
		  }
		});
		Object.defineProperty(exports, "largerEqDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLargerEqGenerated.largerEqDependencies;
		  }
		});
		Object.defineProperty(exports, "lcmDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLcmGenerated.lcmDependencies;
		  }
		});
		Object.defineProperty(exports, "leafCountDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLeafCountGenerated.leafCountDependencies;
		  }
		});
		Object.defineProperty(exports, "leftShiftDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLeftShiftGenerated.leftShiftDependencies;
		  }
		});
		Object.defineProperty(exports, "lgammaDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLgammaGenerated.lgammaDependencies;
		  }
		});
		Object.defineProperty(exports, "log10Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLog10Generated.log10Dependencies;
		  }
		});
		Object.defineProperty(exports, "log1pDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLog1pGenerated.log1pDependencies;
		  }
		});
		Object.defineProperty(exports, "log2Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLog2Generated.log2Dependencies;
		  }
		});
		Object.defineProperty(exports, "logDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLogGenerated.logDependencies;
		  }
		});
		Object.defineProperty(exports, "loschmidtDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLoschmidtGenerated.loschmidtDependencies;
		  }
		});
		Object.defineProperty(exports, "lsolveAllDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLsolveAllGenerated.lsolveAllDependencies;
		  }
		});
		Object.defineProperty(exports, "lsolveDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLsolveGenerated.lsolveDependencies;
		  }
		});
		Object.defineProperty(exports, "lupDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLupGenerated.lupDependencies;
		  }
		});
		Object.defineProperty(exports, "lusolveDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLusolveGenerated.lusolveDependencies;
		  }
		});
		Object.defineProperty(exports, "lyapDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesLyapGenerated.lyapDependencies;
		  }
		});
		Object.defineProperty(exports, "madDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMadGenerated.madDependencies;
		  }
		});
		Object.defineProperty(exports, "magneticConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMagneticConstantGenerated.magneticConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "magneticFluxQuantumDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMagneticFluxQuantumGenerated.magneticFluxQuantumDependencies;
		  }
		});
		Object.defineProperty(exports, "mapDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMapGenerated.mapDependencies;
		  }
		});
		Object.defineProperty(exports, "mapSlicesDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMapSlicesGenerated.mapSlicesDependencies;
		  }
		});
		Object.defineProperty(exports, "mapSlicesTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMapSlicesTransformGenerated.mapSlicesTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "mapTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMapTransformGenerated.mapTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "matrixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMatrixGenerated.matrixDependencies;
		  }
		});
		Object.defineProperty(exports, "matrixFromColumnsDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMatrixFromColumnsGenerated.matrixFromColumnsDependencies;
		  }
		});
		Object.defineProperty(exports, "matrixFromFunctionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMatrixFromFunctionGenerated.matrixFromFunctionDependencies;
		  }
		});
		Object.defineProperty(exports, "matrixFromRowsDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMatrixFromRowsGenerated.matrixFromRowsDependencies;
		  }
		});
		Object.defineProperty(exports, "maxDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMaxGenerated.maxDependencies;
		  }
		});
		Object.defineProperty(exports, "maxTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMaxTransformGenerated.maxTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "meanDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMeanGenerated.meanDependencies;
		  }
		});
		Object.defineProperty(exports, "meanTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMeanTransformGenerated.meanTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "medianDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMedianGenerated.medianDependencies;
		  }
		});
		Object.defineProperty(exports, "minDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMinGenerated.minDependencies;
		  }
		});
		Object.defineProperty(exports, "minTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMinTransformGenerated.minTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "modDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesModGenerated.modDependencies;
		  }
		});
		Object.defineProperty(exports, "modeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesModeGenerated.modeDependencies;
		  }
		});
		Object.defineProperty(exports, "molarMassC12Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMolarMassC12Generated.molarMassC12Dependencies;
		  }
		});
		Object.defineProperty(exports, "molarMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMolarMassGenerated.molarMassDependencies;
		  }
		});
		Object.defineProperty(exports, "molarPlanckConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMolarPlanckConstantGenerated.molarPlanckConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "molarVolumeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMolarVolumeGenerated.molarVolumeDependencies;
		  }
		});
		Object.defineProperty(exports, "multinomialDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMultinomialGenerated.multinomialDependencies;
		  }
		});
		Object.defineProperty(exports, "multiplyDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMultiplyGenerated.multiplyDependencies;
		  }
		});
		Object.defineProperty(exports, "multiplyScalarDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesMultiplyScalarGenerated.multiplyScalarDependencies;
		  }
		});
		Object.defineProperty(exports, "neutronMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNeutronMassGenerated.neutronMassDependencies;
		  }
		});
		Object.defineProperty(exports, "normDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNormGenerated.normDependencies;
		  }
		});
		Object.defineProperty(exports, "notDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNotGenerated.notDependencies;
		  }
		});
		Object.defineProperty(exports, "nthRootDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNthRootGenerated.nthRootDependencies;
		  }
		});
		Object.defineProperty(exports, "nthRootsDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNthRootsGenerated.nthRootsDependencies;
		  }
		});
		Object.defineProperty(exports, "nuclearMagnetonDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNuclearMagnetonGenerated.nuclearMagnetonDependencies;
		  }
		});
		Object.defineProperty(exports, "nullDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNullGenerated.nullDependencies;
		  }
		});
		Object.defineProperty(exports, "nullishDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNullishGenerated.nullishDependencies;
		  }
		});
		Object.defineProperty(exports, "nullishTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNullishTransformGenerated.nullishTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "numDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNumGenerated.numDependencies;
		  }
		});
		Object.defineProperty(exports, "numberDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNumberGenerated.numberDependencies;
		  }
		});
		Object.defineProperty(exports, "numericDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesNumericGenerated.numericDependencies;
		  }
		});
		Object.defineProperty(exports, "octDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesOctGenerated.octDependencies;
		  }
		});
		Object.defineProperty(exports, "onesDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesOnesGenerated.onesDependencies;
		  }
		});
		Object.defineProperty(exports, "orDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesOrGenerated.orDependencies;
		  }
		});
		Object.defineProperty(exports, "orTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesOrTransformGenerated.orTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "parseDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesParseGenerated.parseDependencies;
		  }
		});
		Object.defineProperty(exports, "parserDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesParserGenerated.parserDependencies;
		  }
		});
		Object.defineProperty(exports, "partitionSelectDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPartitionSelectGenerated.partitionSelectDependencies;
		  }
		});
		Object.defineProperty(exports, "permutationsDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPermutationsGenerated.permutationsDependencies;
		  }
		});
		Object.defineProperty(exports, "phiDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPhiGenerated.phiDependencies;
		  }
		});
		Object.defineProperty(exports, "piDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPiGenerated.piDependencies;
		  }
		});
		Object.defineProperty(exports, "pickRandomDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPickRandomGenerated.pickRandomDependencies;
		  }
		});
		Object.defineProperty(exports, "pinvDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPinvGenerated.pinvDependencies;
		  }
		});
		Object.defineProperty(exports, "planckChargeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPlanckChargeGenerated.planckChargeDependencies;
		  }
		});
		Object.defineProperty(exports, "planckConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPlanckConstantGenerated.planckConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "planckLengthDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPlanckLengthGenerated.planckLengthDependencies;
		  }
		});
		Object.defineProperty(exports, "planckMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPlanckMassGenerated.planckMassDependencies;
		  }
		});
		Object.defineProperty(exports, "planckTemperatureDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPlanckTemperatureGenerated.planckTemperatureDependencies;
		  }
		});
		Object.defineProperty(exports, "planckTimeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPlanckTimeGenerated.planckTimeDependencies;
		  }
		});
		Object.defineProperty(exports, "polynomialRootDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPolynomialRootGenerated.polynomialRootDependencies;
		  }
		});
		Object.defineProperty(exports, "powDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPowGenerated.powDependencies;
		  }
		});
		Object.defineProperty(exports, "printDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPrintGenerated.printDependencies;
		  }
		});
		Object.defineProperty(exports, "printTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesPrintTransformGenerated.printTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "prodDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesProdGenerated.prodDependencies;
		  }
		});
		Object.defineProperty(exports, "protonMassDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesProtonMassGenerated.protonMassDependencies;
		  }
		});
		Object.defineProperty(exports, "qrDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesQrGenerated.qrDependencies;
		  }
		});
		Object.defineProperty(exports, "quantileSeqDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesQuantileSeqGenerated.quantileSeqDependencies;
		  }
		});
		Object.defineProperty(exports, "quantileSeqTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesQuantileSeqTransformGenerated.quantileSeqTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "quantumOfCirculationDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesQuantumOfCirculationGenerated.quantumOfCirculationDependencies;
		  }
		});
		Object.defineProperty(exports, "randomDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRandomGenerated.randomDependencies;
		  }
		});
		Object.defineProperty(exports, "randomIntDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRandomIntGenerated.randomIntDependencies;
		  }
		});
		Object.defineProperty(exports, "rangeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRangeGenerated.rangeDependencies;
		  }
		});
		Object.defineProperty(exports, "rangeTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRangeTransformGenerated.rangeTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "rationalizeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRationalizeGenerated.rationalizeDependencies;
		  }
		});
		Object.defineProperty(exports, "reDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesReGenerated.reDependencies;
		  }
		});
		Object.defineProperty(exports, "reducedPlanckConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesReducedPlanckConstantGenerated.reducedPlanckConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "replacerDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesReplacerGenerated.replacerDependencies;
		  }
		});
		Object.defineProperty(exports, "reshapeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesReshapeGenerated.reshapeDependencies;
		  }
		});
		Object.defineProperty(exports, "resizeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesResizeGenerated.resizeDependencies;
		  }
		});
		Object.defineProperty(exports, "resolveDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesResolveGenerated.resolveDependencies;
		  }
		});
		Object.defineProperty(exports, "reviverDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesReviverGenerated.reviverDependencies;
		  }
		});
		Object.defineProperty(exports, "rightArithShiftDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRightArithShiftGenerated.rightArithShiftDependencies;
		  }
		});
		Object.defineProperty(exports, "rightLogShiftDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRightLogShiftGenerated.rightLogShiftDependencies;
		  }
		});
		Object.defineProperty(exports, "rotateDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRotateGenerated.rotateDependencies;
		  }
		});
		Object.defineProperty(exports, "rotationMatrixDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRotationMatrixGenerated.rotationMatrixDependencies;
		  }
		});
		Object.defineProperty(exports, "roundDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRoundGenerated.roundDependencies;
		  }
		});
		Object.defineProperty(exports, "rowDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRowGenerated.rowDependencies;
		  }
		});
		Object.defineProperty(exports, "rowTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRowTransformGenerated.rowTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "rydbergDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesRydbergGenerated.rydbergDependencies;
		  }
		});
		Object.defineProperty(exports, "sackurTetrodeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSackurTetrodeGenerated.sackurTetrodeDependencies;
		  }
		});
		Object.defineProperty(exports, "schurDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSchurGenerated.schurDependencies;
		  }
		});
		Object.defineProperty(exports, "secDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSecGenerated.secDependencies;
		  }
		});
		Object.defineProperty(exports, "sechDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSechGenerated.sechDependencies;
		  }
		});
		Object.defineProperty(exports, "secondRadiationDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSecondRadiationGenerated.secondRadiationDependencies;
		  }
		});
		Object.defineProperty(exports, "setCartesianDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetCartesianGenerated.setCartesianDependencies;
		  }
		});
		Object.defineProperty(exports, "setDifferenceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetDifferenceGenerated.setDifferenceDependencies;
		  }
		});
		Object.defineProperty(exports, "setDistinctDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetDistinctGenerated.setDistinctDependencies;
		  }
		});
		Object.defineProperty(exports, "setIntersectDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetIntersectGenerated.setIntersectDependencies;
		  }
		});
		Object.defineProperty(exports, "setIsSubsetDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetIsSubsetGenerated.setIsSubsetDependencies;
		  }
		});
		Object.defineProperty(exports, "setMultiplicityDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetMultiplicityGenerated.setMultiplicityDependencies;
		  }
		});
		Object.defineProperty(exports, "setPowersetDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetPowersetGenerated.setPowersetDependencies;
		  }
		});
		Object.defineProperty(exports, "setSizeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetSizeGenerated.setSizeDependencies;
		  }
		});
		Object.defineProperty(exports, "setSymDifferenceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetSymDifferenceGenerated.setSymDifferenceDependencies;
		  }
		});
		Object.defineProperty(exports, "setUnionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSetUnionGenerated.setUnionDependencies;
		  }
		});
		Object.defineProperty(exports, "signDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSignGenerated.signDependencies;
		  }
		});
		Object.defineProperty(exports, "simplifyConstantDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSimplifyConstantGenerated.simplifyConstantDependencies;
		  }
		});
		Object.defineProperty(exports, "simplifyCoreDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSimplifyCoreGenerated.simplifyCoreDependencies;
		  }
		});
		Object.defineProperty(exports, "simplifyDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSimplifyGenerated.simplifyDependencies;
		  }
		});
		Object.defineProperty(exports, "sinDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSinGenerated.sinDependencies;
		  }
		});
		Object.defineProperty(exports, "sinhDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSinhGenerated.sinhDependencies;
		  }
		});
		Object.defineProperty(exports, "sizeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSizeGenerated.sizeDependencies;
		  }
		});
		Object.defineProperty(exports, "sluDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSluGenerated.sluDependencies;
		  }
		});
		Object.defineProperty(exports, "smallerDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSmallerGenerated.smallerDependencies;
		  }
		});
		Object.defineProperty(exports, "smallerEqDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSmallerEqGenerated.smallerEqDependencies;
		  }
		});
		Object.defineProperty(exports, "solveODEDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSolveODEGenerated.solveODEDependencies;
		  }
		});
		Object.defineProperty(exports, "sortDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSortGenerated.sortDependencies;
		  }
		});
		Object.defineProperty(exports, "sparseDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSparseGenerated.sparseDependencies;
		  }
		});
		Object.defineProperty(exports, "speedOfLightDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSpeedOfLightGenerated.speedOfLightDependencies;
		  }
		});
		Object.defineProperty(exports, "splitUnitDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSplitUnitGenerated.splitUnitDependencies;
		  }
		});
		Object.defineProperty(exports, "sqrtDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSqrtGenerated.sqrtDependencies;
		  }
		});
		Object.defineProperty(exports, "sqrtmDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSqrtmGenerated.sqrtmDependencies;
		  }
		});
		Object.defineProperty(exports, "squareDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSquareGenerated.squareDependencies;
		  }
		});
		Object.defineProperty(exports, "squeezeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSqueezeGenerated.squeezeDependencies;
		  }
		});
		Object.defineProperty(exports, "stdDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesStdGenerated.stdDependencies;
		  }
		});
		Object.defineProperty(exports, "stdTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesStdTransformGenerated.stdTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "stefanBoltzmannDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesStefanBoltzmannGenerated.stefanBoltzmannDependencies;
		  }
		});
		Object.defineProperty(exports, "stirlingS2Dependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesStirlingS2Generated.stirlingS2Dependencies;
		  }
		});
		Object.defineProperty(exports, "stringDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesStringGenerated.stringDependencies;
		  }
		});
		Object.defineProperty(exports, "subsetDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSubsetGenerated.subsetDependencies;
		  }
		});
		Object.defineProperty(exports, "subsetTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSubsetTransformGenerated.subsetTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "subtractDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSubtractGenerated.subtractDependencies;
		  }
		});
		Object.defineProperty(exports, "subtractScalarDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSubtractScalarGenerated.subtractScalarDependencies;
		  }
		});
		Object.defineProperty(exports, "sumDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSumGenerated.sumDependencies;
		  }
		});
		Object.defineProperty(exports, "sumTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSumTransformGenerated.sumTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "sylvesterDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSylvesterGenerated.sylvesterDependencies;
		  }
		});
		Object.defineProperty(exports, "symbolicEqualDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesSymbolicEqualGenerated.symbolicEqualDependencies;
		  }
		});
		Object.defineProperty(exports, "tanDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTanGenerated.tanDependencies;
		  }
		});
		Object.defineProperty(exports, "tanhDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTanhGenerated.tanhDependencies;
		  }
		});
		Object.defineProperty(exports, "tauDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTauGenerated.tauDependencies;
		  }
		});
		Object.defineProperty(exports, "thomsonCrossSectionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesThomsonCrossSectionGenerated.thomsonCrossSectionDependencies;
		  }
		});
		Object.defineProperty(exports, "toBestDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesToBestGenerated.toBestDependencies;
		  }
		});
		Object.defineProperty(exports, "toDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesToGenerated.toDependencies;
		  }
		});
		Object.defineProperty(exports, "traceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTraceGenerated.traceDependencies;
		  }
		});
		Object.defineProperty(exports, "transposeDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTransposeGenerated.transposeDependencies;
		  }
		});
		Object.defineProperty(exports, "trueDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTrueGenerated.trueDependencies;
		  }
		});
		Object.defineProperty(exports, "typeOfDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTypeOfGenerated.typeOfDependencies;
		  }
		});
		Object.defineProperty(exports, "typedDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesTypedGenerated.typedDependencies;
		  }
		});
		Object.defineProperty(exports, "unaryMinusDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUnaryMinusGenerated.unaryMinusDependencies;
		  }
		});
		Object.defineProperty(exports, "unaryPlusDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUnaryPlusGenerated.unaryPlusDependencies;
		  }
		});
		Object.defineProperty(exports, "unequalDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUnequalGenerated.unequalDependencies;
		  }
		});
		Object.defineProperty(exports, "unitDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUnitFunctionGenerated.unitDependencies;
		  }
		});
		Object.defineProperty(exports, "usolveAllDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUsolveAllGenerated.usolveAllDependencies;
		  }
		});
		Object.defineProperty(exports, "usolveDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesUsolveGenerated.usolveDependencies;
		  }
		});
		Object.defineProperty(exports, "vacuumImpedanceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesVacuumImpedanceGenerated.vacuumImpedanceDependencies;
		  }
		});
		Object.defineProperty(exports, "varianceDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesVarianceGenerated.varianceDependencies;
		  }
		});
		Object.defineProperty(exports, "varianceTransformDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesVarianceTransformGenerated.varianceTransformDependencies;
		  }
		});
		Object.defineProperty(exports, "versionDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesVersionGenerated.versionDependencies;
		  }
		});
		Object.defineProperty(exports, "weakMixingAngleDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesWeakMixingAngleGenerated.weakMixingAngleDependencies;
		  }
		});
		Object.defineProperty(exports, "wienDisplacementDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesWienDisplacementGenerated.wienDisplacementDependencies;
		  }
		});
		Object.defineProperty(exports, "xgcdDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesXgcdGenerated.xgcdDependencies;
		  }
		});
		Object.defineProperty(exports, "xorDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesXorGenerated.xorDependencies;
		  }
		});
		Object.defineProperty(exports, "zerosDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesZerosGenerated.zerosDependencies;
		  }
		});
		Object.defineProperty(exports, "zetaDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesZetaGenerated.zetaDependencies;
		  }
		});
		Object.defineProperty(exports, "zpk2tfDependencies", {
		  enumerable: true,
		  get: function () {
		    return _dependenciesZpk2tfGenerated.zpk2tfDependencies;
		  }
		});
		var _dependenciesAbsGenerated = requireDependenciesAbs_generated();
		var _dependenciesAccessorNodeGenerated = requireDependenciesAccessorNode_generated();
		var _dependenciesAcosGenerated = requireDependenciesAcos_generated();
		var _dependenciesAcoshGenerated = requireDependenciesAcosh_generated();
		var _dependenciesAcotGenerated = requireDependenciesAcot_generated();
		var _dependenciesAcothGenerated = requireDependenciesAcoth_generated();
		var _dependenciesAcscGenerated = requireDependenciesAcsc_generated();
		var _dependenciesAcschGenerated = requireDependenciesAcsch_generated();
		var _dependenciesAddGenerated = requireDependenciesAdd_generated();
		var _dependenciesAddScalarGenerated = requireDependenciesAddScalar_generated();
		var _dependenciesAndGenerated = requireDependenciesAnd_generated();
		var _dependenciesAndTransformGenerated = requireDependenciesAndTransform_generated();
		var _dependenciesArgGenerated = requireDependenciesArg_generated();
		var _dependenciesArrayNodeGenerated = requireDependenciesArrayNode_generated();
		var _dependenciesAsecGenerated = requireDependenciesAsec_generated();
		var _dependenciesAsechGenerated = requireDependenciesAsech_generated();
		var _dependenciesAsinGenerated = requireDependenciesAsin_generated();
		var _dependenciesAsinhGenerated = requireDependenciesAsinh_generated();
		var _dependenciesAssignmentNodeGenerated = requireDependenciesAssignmentNode_generated();
		var _dependenciesAtanGenerated = requireDependenciesAtan_generated();
		var _dependenciesAtan2Generated = requireDependenciesAtan2_generated();
		var _dependenciesAtanhGenerated = requireDependenciesAtanh_generated();
		var _dependenciesAtomicMassGenerated = requireDependenciesAtomicMass_generated();
		var _dependenciesAvogadroGenerated = requireDependenciesAvogadro_generated();
		var _dependenciesBellNumbersGenerated = requireDependenciesBellNumbers_generated();
		var _dependenciesBernoulliGenerated = requireDependenciesBernoulli_generated();
		var _dependenciesBigNumberClassGenerated = requireDependenciesBigNumberClass_generated();
		var _dependenciesBigintGenerated = requireDependenciesBigint_generated();
		var _dependenciesBignumberGenerated = requireDependenciesBignumber_generated();
		var _dependenciesBinGenerated = requireDependenciesBin_generated();
		var _dependenciesBitAndGenerated = requireDependenciesBitAnd_generated();
		var _dependenciesBitAndTransformGenerated = requireDependenciesBitAndTransform_generated();
		var _dependenciesBitNotGenerated = requireDependenciesBitNot_generated();
		var _dependenciesBitOrGenerated = requireDependenciesBitOr_generated();
		var _dependenciesBitOrTransformGenerated = requireDependenciesBitOrTransform_generated();
		var _dependenciesBitXorGenerated = requireDependenciesBitXor_generated();
		var _dependenciesBlockNodeGenerated = requireDependenciesBlockNode_generated();
		var _dependenciesBohrMagnetonGenerated = requireDependenciesBohrMagneton_generated();
		var _dependenciesBohrRadiusGenerated = requireDependenciesBohrRadius_generated();
		var _dependenciesBoltzmannGenerated = requireDependenciesBoltzmann_generated();
		var _dependenciesBooleanGenerated = requireDependenciesBoolean_generated();
		var _dependenciesCatalanGenerated = requireDependenciesCatalan_generated();
		var _dependenciesCbrtGenerated = requireDependenciesCbrt_generated();
		var _dependenciesCeilGenerated = requireDependenciesCeil_generated();
		var _dependenciesChainGenerated = requireDependenciesChain_generated();
		var _dependenciesChainClassGenerated = requireDependenciesChainClass_generated();
		var _dependenciesClassicalElectronRadiusGenerated = requireDependenciesClassicalElectronRadius_generated();
		var _dependenciesCloneGenerated = requireDependenciesClone_generated();
		var _dependenciesColumnGenerated = requireDependenciesColumn_generated();
		var _dependenciesColumnTransformGenerated = requireDependenciesColumnTransform_generated();
		var _dependenciesCombinationsGenerated = requireDependenciesCombinations_generated();
		var _dependenciesCombinationsWithRepGenerated = requireDependenciesCombinationsWithRep_generated();
		var _dependenciesCompareGenerated = requireDependenciesCompare_generated();
		var _dependenciesCompareNaturalGenerated = requireDependenciesCompareNatural_generated();
		var _dependenciesCompareTextGenerated = requireDependenciesCompareText_generated();
		var _dependenciesCompileGenerated = requireDependenciesCompile_generated();
		var _dependenciesComplexGenerated = requireDependenciesComplex_generated();
		var _dependenciesComplexClassGenerated = requireDependenciesComplexClass_generated();
		var _dependenciesCompositionGenerated = requireDependenciesComposition_generated();
		var _dependenciesConcatGenerated = requireDependenciesConcat_generated();
		var _dependenciesConcatTransformGenerated = requireDependenciesConcatTransform_generated();
		var _dependenciesConditionalNodeGenerated = requireDependenciesConditionalNode_generated();
		var _dependenciesConductanceQuantumGenerated = requireDependenciesConductanceQuantum_generated();
		var _dependenciesConjGenerated = requireDependenciesConj_generated();
		var _dependenciesConstantNodeGenerated = requireDependenciesConstantNode_generated();
		var _dependenciesCorrGenerated = requireDependenciesCorr_generated();
		var _dependenciesCosGenerated = requireDependenciesCos_generated();
		var _dependenciesCoshGenerated = requireDependenciesCosh_generated();
		var _dependenciesCotGenerated = requireDependenciesCot_generated();
		var _dependenciesCothGenerated = requireDependenciesCoth_generated();
		var _dependenciesCoulombGenerated = requireDependenciesCoulomb_generated();
		var _dependenciesCoulombConstantGenerated = requireDependenciesCoulombConstant_generated();
		var _dependenciesCountGenerated = requireDependenciesCount_generated();
		var _dependenciesCreateUnitGenerated = requireDependenciesCreateUnit_generated();
		var _dependenciesCrossGenerated = requireDependenciesCross_generated();
		var _dependenciesCscGenerated = requireDependenciesCsc_generated();
		var _dependenciesCschGenerated = requireDependenciesCsch_generated();
		var _dependenciesCtransposeGenerated = requireDependenciesCtranspose_generated();
		var _dependenciesCubeGenerated = requireDependenciesCube_generated();
		var _dependenciesCumSumGenerated = requireDependenciesCumSum_generated();
		var _dependenciesCumSumTransformGenerated = requireDependenciesCumSumTransform_generated();
		var _dependenciesDeepEqualGenerated = requireDependenciesDeepEqual_generated();
		var _dependenciesDenGenerated = requireDependenciesDen_generated();
		var _dependenciesDenseMatrixClassGenerated = requireDependenciesDenseMatrixClass_generated();
		var _dependenciesDerivativeGenerated = requireDependenciesDerivative_generated();
		var _dependenciesDetGenerated = requireDependenciesDet_generated();
		var _dependenciesDeuteronMassGenerated = requireDependenciesDeuteronMass_generated();
		var _dependenciesDiagGenerated = requireDependenciesDiag_generated();
		var _dependenciesDiffGenerated = requireDependenciesDiff_generated();
		var _dependenciesDiffTransformGenerated = requireDependenciesDiffTransform_generated();
		var _dependenciesDistanceGenerated = requireDependenciesDistance_generated();
		var _dependenciesDivideGenerated = requireDependenciesDivide_generated();
		var _dependenciesDivideScalarGenerated = requireDependenciesDivideScalar_generated();
		var _dependenciesDotGenerated = requireDependenciesDot_generated();
		var _dependenciesDotDivideGenerated = requireDependenciesDotDivide_generated();
		var _dependenciesDotMultiplyGenerated = requireDependenciesDotMultiply_generated();
		var _dependenciesDotPowGenerated = requireDependenciesDotPow_generated();
		var _dependenciesEGenerated = requireDependenciesE_generated();
		var _dependenciesEfimovFactorGenerated = requireDependenciesEfimovFactor_generated();
		var _dependenciesEigsGenerated = requireDependenciesEigs_generated();
		var _dependenciesElectricConstantGenerated = requireDependenciesElectricConstant_generated();
		var _dependenciesElectronMassGenerated = requireDependenciesElectronMass_generated();
		var _dependenciesElementaryChargeGenerated = requireDependenciesElementaryCharge_generated();
		var _dependenciesEqualGenerated = requireDependenciesEqual_generated();
		var _dependenciesEqualScalarGenerated = requireDependenciesEqualScalar_generated();
		var _dependenciesEqualTextGenerated = requireDependenciesEqualText_generated();
		var _dependenciesErfGenerated = requireDependenciesErf_generated();
		var _dependenciesEvaluateGenerated = requireDependenciesEvaluate_generated();
		var _dependenciesExpGenerated = requireDependenciesExp_generated();
		var _dependenciesExpmGenerated = requireDependenciesExpm_generated();
		var _dependenciesExpm1Generated = requireDependenciesExpm1_generated();
		var _dependenciesFactorialGenerated = requireDependenciesFactorial_generated();
		var _dependenciesFalseGenerated = requireDependenciesFalse_generated();
		var _dependenciesFaradayGenerated = requireDependenciesFaraday_generated();
		var _dependenciesFermiCouplingGenerated = requireDependenciesFermiCoupling_generated();
		var _dependenciesFftGenerated = requireDependenciesFft_generated();
		var _dependenciesFibonacciHeapClassGenerated = requireDependenciesFibonacciHeapClass_generated();
		var _dependenciesFilterGenerated = requireDependenciesFilter_generated();
		var _dependenciesFilterTransformGenerated = requireDependenciesFilterTransform_generated();
		var _dependenciesFineStructureGenerated = requireDependenciesFineStructure_generated();
		var _dependenciesFirstRadiationGenerated = requireDependenciesFirstRadiation_generated();
		var _dependenciesFixGenerated = requireDependenciesFix_generated();
		var _dependenciesFlattenGenerated = requireDependenciesFlatten_generated();
		var _dependenciesFloorGenerated = requireDependenciesFloor_generated();
		var _dependenciesForEachGenerated = requireDependenciesForEach_generated();
		var _dependenciesForEachTransformGenerated = requireDependenciesForEachTransform_generated();
		var _dependenciesFormatGenerated = requireDependenciesFormat_generated();
		var _dependenciesFractionGenerated = requireDependenciesFraction_generated();
		var _dependenciesFractionClassGenerated = requireDependenciesFractionClass_generated();
		var _dependenciesFreqzGenerated = requireDependenciesFreqz_generated();
		var _dependenciesFunctionAssignmentNodeGenerated = requireDependenciesFunctionAssignmentNode_generated();
		var _dependenciesFunctionNodeGenerated = requireDependenciesFunctionNode_generated();
		var _dependenciesGammaGenerated = requireDependenciesGamma_generated();
		var _dependenciesGasConstantGenerated = requireDependenciesGasConstant_generated();
		var _dependenciesGcdGenerated = requireDependenciesGcd_generated();
		var _dependenciesGetMatrixDataTypeGenerated = requireDependenciesGetMatrixDataType_generated();
		var _dependenciesGravitationConstantGenerated = requireDependenciesGravitationConstant_generated();
		var _dependenciesGravityGenerated = requireDependenciesGravity_generated();
		var _dependenciesHartreeEnergyGenerated = requireDependenciesHartreeEnergy_generated();
		var _dependenciesHasNumericValueGenerated = requireDependenciesHasNumericValue_generated();
		var _dependenciesHelpGenerated = requireDependenciesHelp_generated();
		var _dependenciesHelpClassGenerated = requireDependenciesHelpClass_generated();
		var _dependenciesHexGenerated = requireDependenciesHex_generated();
		var _dependenciesHypotGenerated = requireDependenciesHypot_generated();
		var _dependenciesIGenerated = requireDependenciesI_generated();
		var _dependenciesIdentityGenerated = requireDependenciesIdentity_generated();
		var _dependenciesIfftGenerated = requireDependenciesIfft_generated();
		var _dependenciesImGenerated = requireDependenciesIm_generated();
		var _dependenciesImmutableDenseMatrixClassGenerated = requireDependenciesImmutableDenseMatrixClass_generated();
		var _dependenciesIndexGenerated = requireDependenciesIndex_generated();
		var _dependenciesIndexClassGenerated = requireDependenciesIndexClass_generated();
		var _dependenciesIndexNodeGenerated = requireDependenciesIndexNode_generated();
		var _dependenciesIndexTransformGenerated = requireDependenciesIndexTransform_generated();
		var _dependenciesInfinityGenerated = requireDependenciesInfinity_generated();
		var _dependenciesIntersectGenerated = requireDependenciesIntersect_generated();
		var _dependenciesInvGenerated = requireDependenciesInv_generated();
		var _dependenciesInverseConductanceQuantumGenerated = requireDependenciesInverseConductanceQuantum_generated();
		var _dependenciesInvmodGenerated = requireDependenciesInvmod_generated();
		var _dependenciesIsBoundedGenerated = requireDependenciesIsBounded_generated();
		var _dependenciesIsFiniteGenerated = requireDependenciesIsFinite_generated();
		var _dependenciesIsIntegerGenerated = requireDependenciesIsInteger_generated();
		var _dependenciesIsNaNGenerated = requireDependenciesIsNaN_generated();
		var _dependenciesIsNegativeGenerated = requireDependenciesIsNegative_generated();
		var _dependenciesIsNumericGenerated = requireDependenciesIsNumeric_generated();
		var _dependenciesIsPositiveGenerated = requireDependenciesIsPositive_generated();
		var _dependenciesIsPrimeGenerated = requireDependenciesIsPrime_generated();
		var _dependenciesIsZeroGenerated = requireDependenciesIsZero_generated();
		var _dependenciesKldivergenceGenerated = requireDependenciesKldivergence_generated();
		var _dependenciesKlitzingGenerated = requireDependenciesKlitzing_generated();
		var _dependenciesKronGenerated = requireDependenciesKron_generated();
		var _dependenciesLN10Generated = requireDependenciesLN10_generated();
		var _dependenciesLN2Generated = requireDependenciesLN2_generated();
		var _dependenciesLOG10EGenerated = requireDependenciesLOG10E_generated();
		var _dependenciesLOG2EGenerated = requireDependenciesLOG2E_generated();
		var _dependenciesLargerGenerated = requireDependenciesLarger_generated();
		var _dependenciesLargerEqGenerated = requireDependenciesLargerEq_generated();
		var _dependenciesLcmGenerated = requireDependenciesLcm_generated();
		var _dependenciesLeafCountGenerated = requireDependenciesLeafCount_generated();
		var _dependenciesLeftShiftGenerated = requireDependenciesLeftShift_generated();
		var _dependenciesLgammaGenerated = requireDependenciesLgamma_generated();
		var _dependenciesLogGenerated = requireDependenciesLog_generated();
		var _dependenciesLog10Generated = requireDependenciesLog10_generated();
		var _dependenciesLog1pGenerated = requireDependenciesLog1p_generated();
		var _dependenciesLog2Generated = requireDependenciesLog2_generated();
		var _dependenciesLoschmidtGenerated = requireDependenciesLoschmidt_generated();
		var _dependenciesLsolveGenerated = requireDependenciesLsolve_generated();
		var _dependenciesLsolveAllGenerated = requireDependenciesLsolveAll_generated();
		var _dependenciesLupGenerated = requireDependenciesLup_generated();
		var _dependenciesLusolveGenerated = requireDependenciesLusolve_generated();
		var _dependenciesLyapGenerated = requireDependenciesLyap_generated();
		var _dependenciesMadGenerated = requireDependenciesMad_generated();
		var _dependenciesMagneticConstantGenerated = requireDependenciesMagneticConstant_generated();
		var _dependenciesMagneticFluxQuantumGenerated = requireDependenciesMagneticFluxQuantum_generated();
		var _dependenciesMapGenerated = requireDependenciesMap_generated();
		var _dependenciesMapSlicesGenerated = requireDependenciesMapSlices_generated();
		var _dependenciesMapSlicesTransformGenerated = requireDependenciesMapSlicesTransform_generated();
		var _dependenciesMapTransformGenerated = requireDependenciesMapTransform_generated();
		var _dependenciesMatrixGenerated = requireDependenciesMatrix_generated();
		var _dependenciesMatrixClassGenerated = requireDependenciesMatrixClass_generated();
		var _dependenciesMatrixFromColumnsGenerated = requireDependenciesMatrixFromColumns_generated();
		var _dependenciesMatrixFromFunctionGenerated = requireDependenciesMatrixFromFunction_generated();
		var _dependenciesMatrixFromRowsGenerated = requireDependenciesMatrixFromRows_generated();
		var _dependenciesMaxGenerated = requireDependenciesMax_generated();
		var _dependenciesMaxTransformGenerated = requireDependenciesMaxTransform_generated();
		var _dependenciesMeanGenerated = requireDependenciesMean_generated();
		var _dependenciesMeanTransformGenerated = requireDependenciesMeanTransform_generated();
		var _dependenciesMedianGenerated = requireDependenciesMedian_generated();
		var _dependenciesMinGenerated = requireDependenciesMin_generated();
		var _dependenciesMinTransformGenerated = requireDependenciesMinTransform_generated();
		var _dependenciesModGenerated = requireDependenciesMod_generated();
		var _dependenciesModeGenerated = requireDependenciesMode_generated();
		var _dependenciesMolarMassGenerated = requireDependenciesMolarMass_generated();
		var _dependenciesMolarMassC12Generated = requireDependenciesMolarMassC12_generated();
		var _dependenciesMolarPlanckConstantGenerated = requireDependenciesMolarPlanckConstant_generated();
		var _dependenciesMolarVolumeGenerated = requireDependenciesMolarVolume_generated();
		var _dependenciesMultinomialGenerated = requireDependenciesMultinomial_generated();
		var _dependenciesMultiplyGenerated = requireDependenciesMultiply_generated();
		var _dependenciesMultiplyScalarGenerated = requireDependenciesMultiplyScalar_generated();
		var _dependenciesNaNGenerated = requireDependenciesNaN_generated();
		var _dependenciesNeutronMassGenerated = requireDependenciesNeutronMass_generated();
		var _dependenciesNodeGenerated = requireDependenciesNode_generated();
		var _dependenciesNormGenerated = requireDependenciesNorm_generated();
		var _dependenciesNotGenerated = requireDependenciesNot_generated();
		var _dependenciesNthRootGenerated = requireDependenciesNthRoot_generated();
		var _dependenciesNthRootsGenerated = requireDependenciesNthRoots_generated();
		var _dependenciesNuclearMagnetonGenerated = requireDependenciesNuclearMagneton_generated();
		var _dependenciesNullGenerated = requireDependenciesNull_generated();
		var _dependenciesNullishGenerated = requireDependenciesNullish_generated();
		var _dependenciesNullishTransformGenerated = requireDependenciesNullishTransform_generated();
		var _dependenciesNumGenerated = requireDependenciesNum_generated();
		var _dependenciesNumberGenerated = requireDependenciesNumber_generated();
		var _dependenciesNumericGenerated = requireDependenciesNumeric_generated();
		var _dependenciesObjectNodeGenerated = requireDependenciesObjectNode_generated();
		var _dependenciesOctGenerated = requireDependenciesOct_generated();
		var _dependenciesOnesGenerated = requireDependenciesOnes_generated();
		var _dependenciesOperatorNodeGenerated = requireDependenciesOperatorNode_generated();
		var _dependenciesOrGenerated = requireDependenciesOr_generated();
		var _dependenciesOrTransformGenerated = requireDependenciesOrTransform_generated();
		var _dependenciesParenthesisNodeGenerated = requireDependenciesParenthesisNode_generated();
		var _dependenciesParseGenerated = requireDependenciesParse_generated();
		var _dependenciesParserGenerated = requireDependenciesParser_generated();
		var _dependenciesParserClassGenerated = requireDependenciesParserClass_generated();
		var _dependenciesPartitionSelectGenerated = requireDependenciesPartitionSelect_generated();
		var _dependenciesPermutationsGenerated = requireDependenciesPermutations_generated();
		var _dependenciesPhiGenerated = requireDependenciesPhi_generated();
		var _dependenciesPiGenerated = requireDependenciesPi_generated();
		var _dependenciesPickRandomGenerated = requireDependenciesPickRandom_generated();
		var _dependenciesPinvGenerated = requireDependenciesPinv_generated();
		var _dependenciesPlanckChargeGenerated = requireDependenciesPlanckCharge_generated();
		var _dependenciesPlanckConstantGenerated = requireDependenciesPlanckConstant_generated();
		var _dependenciesPlanckLengthGenerated = requireDependenciesPlanckLength_generated();
		var _dependenciesPlanckMassGenerated = requireDependenciesPlanckMass_generated();
		var _dependenciesPlanckTemperatureGenerated = requireDependenciesPlanckTemperature_generated();
		var _dependenciesPlanckTimeGenerated = requireDependenciesPlanckTime_generated();
		var _dependenciesPolynomialRootGenerated = requireDependenciesPolynomialRoot_generated();
		var _dependenciesPowGenerated = requireDependenciesPow_generated();
		var _dependenciesPrintGenerated = requireDependenciesPrint_generated();
		var _dependenciesPrintTransformGenerated = requireDependenciesPrintTransform_generated();
		var _dependenciesProdGenerated = requireDependenciesProd_generated();
		var _dependenciesProtonMassGenerated = requireDependenciesProtonMass_generated();
		var _dependenciesQrGenerated = requireDependenciesQr_generated();
		var _dependenciesQuantileSeqGenerated = requireDependenciesQuantileSeq_generated();
		var _dependenciesQuantileSeqTransformGenerated = requireDependenciesQuantileSeqTransform_generated();
		var _dependenciesQuantumOfCirculationGenerated = requireDependenciesQuantumOfCirculation_generated();
		var _dependenciesRandomGenerated = requireDependenciesRandom_generated();
		var _dependenciesRandomIntGenerated = requireDependenciesRandomInt_generated();
		var _dependenciesRangeGenerated = requireDependenciesRange_generated();
		var _dependenciesRangeClassGenerated = requireDependenciesRangeClass_generated();
		var _dependenciesRangeNodeGenerated = requireDependenciesRangeNode_generated();
		var _dependenciesRangeTransformGenerated = requireDependenciesRangeTransform_generated();
		var _dependenciesRationalizeGenerated = requireDependenciesRationalize_generated();
		var _dependenciesReGenerated = requireDependenciesRe_generated();
		var _dependenciesReducedPlanckConstantGenerated = requireDependenciesReducedPlanckConstant_generated();
		var _dependenciesRelationalNodeGenerated = requireDependenciesRelationalNode_generated();
		var _dependenciesReplacerGenerated = requireDependenciesReplacer_generated();
		var _dependenciesReshapeGenerated = requireDependenciesReshape_generated();
		var _dependenciesResizeGenerated = requireDependenciesResize_generated();
		var _dependenciesResolveGenerated = requireDependenciesResolve_generated();
		var _dependenciesResultSetGenerated = requireDependenciesResultSet_generated();
		var _dependenciesReviverGenerated = requireDependenciesReviver_generated();
		var _dependenciesRightArithShiftGenerated = requireDependenciesRightArithShift_generated();
		var _dependenciesRightLogShiftGenerated = requireDependenciesRightLogShift_generated();
		var _dependenciesRotateGenerated = requireDependenciesRotate_generated();
		var _dependenciesRotationMatrixGenerated = requireDependenciesRotationMatrix_generated();
		var _dependenciesRoundGenerated = requireDependenciesRound_generated();
		var _dependenciesRowGenerated = requireDependenciesRow_generated();
		var _dependenciesRowTransformGenerated = requireDependenciesRowTransform_generated();
		var _dependenciesRydbergGenerated = requireDependenciesRydberg_generated();
		var _dependenciesSQRT1_2Generated = requireDependenciesSQRT1_2_generated();
		var _dependenciesSQRT2Generated = requireDependenciesSQRT2_generated();
		var _dependenciesSackurTetrodeGenerated = requireDependenciesSackurTetrode_generated();
		var _dependenciesSchurGenerated = requireDependenciesSchur_generated();
		var _dependenciesSecGenerated = requireDependenciesSec_generated();
		var _dependenciesSechGenerated = requireDependenciesSech_generated();
		var _dependenciesSecondRadiationGenerated = requireDependenciesSecondRadiation_generated();
		var _dependenciesSetCartesianGenerated = requireDependenciesSetCartesian_generated();
		var _dependenciesSetDifferenceGenerated = requireDependenciesSetDifference_generated();
		var _dependenciesSetDistinctGenerated = requireDependenciesSetDistinct_generated();
		var _dependenciesSetIntersectGenerated = requireDependenciesSetIntersect_generated();
		var _dependenciesSetIsSubsetGenerated = requireDependenciesSetIsSubset_generated();
		var _dependenciesSetMultiplicityGenerated = requireDependenciesSetMultiplicity_generated();
		var _dependenciesSetPowersetGenerated = requireDependenciesSetPowerset_generated();
		var _dependenciesSetSizeGenerated = requireDependenciesSetSize_generated();
		var _dependenciesSetSymDifferenceGenerated = requireDependenciesSetSymDifference_generated();
		var _dependenciesSetUnionGenerated = requireDependenciesSetUnion_generated();
		var _dependenciesSignGenerated = requireDependenciesSign_generated();
		var _dependenciesSimplifyGenerated = requireDependenciesSimplify_generated();
		var _dependenciesSimplifyConstantGenerated = requireDependenciesSimplifyConstant_generated();
		var _dependenciesSimplifyCoreGenerated = requireDependenciesSimplifyCore_generated();
		var _dependenciesSinGenerated = requireDependenciesSin_generated();
		var _dependenciesSinhGenerated = requireDependenciesSinh_generated();
		var _dependenciesSizeGenerated = requireDependenciesSize_generated();
		var _dependenciesSluGenerated = requireDependenciesSlu_generated();
		var _dependenciesSmallerGenerated = requireDependenciesSmaller_generated();
		var _dependenciesSmallerEqGenerated = requireDependenciesSmallerEq_generated();
		var _dependenciesSolveODEGenerated = requireDependenciesSolveODE_generated();
		var _dependenciesSortGenerated = requireDependenciesSort_generated();
		var _dependenciesSpaClassGenerated = requireDependenciesSpaClass_generated();
		var _dependenciesSparseGenerated = requireDependenciesSparse_generated();
		var _dependenciesSparseMatrixClassGenerated = requireDependenciesSparseMatrixClass_generated();
		var _dependenciesSpeedOfLightGenerated = requireDependenciesSpeedOfLight_generated();
		var _dependenciesSplitUnitGenerated = requireDependenciesSplitUnit_generated();
		var _dependenciesSqrtGenerated = requireDependenciesSqrt_generated();
		var _dependenciesSqrtmGenerated = requireDependenciesSqrtm_generated();
		var _dependenciesSquareGenerated = requireDependenciesSquare_generated();
		var _dependenciesSqueezeGenerated = requireDependenciesSqueeze_generated();
		var _dependenciesStdGenerated = requireDependenciesStd_generated();
		var _dependenciesStdTransformGenerated = requireDependenciesStdTransform_generated();
		var _dependenciesStefanBoltzmannGenerated = requireDependenciesStefanBoltzmann_generated();
		var _dependenciesStirlingS2Generated = requireDependenciesStirlingS2_generated();
		var _dependenciesStringGenerated = requireDependenciesString_generated();
		var _dependenciesSubsetGenerated = requireDependenciesSubset_generated();
		var _dependenciesSubsetTransformGenerated = requireDependenciesSubsetTransform_generated();
		var _dependenciesSubtractGenerated = requireDependenciesSubtract_generated();
		var _dependenciesSubtractScalarGenerated = requireDependenciesSubtractScalar_generated();
		var _dependenciesSumGenerated = requireDependenciesSum_generated();
		var _dependenciesSumTransformGenerated = requireDependenciesSumTransform_generated();
		var _dependenciesSylvesterGenerated = requireDependenciesSylvester_generated();
		var _dependenciesSymbolNodeGenerated = requireDependenciesSymbolNode_generated();
		var _dependenciesSymbolicEqualGenerated = requireDependenciesSymbolicEqual_generated();
		var _dependenciesTanGenerated = requireDependenciesTan_generated();
		var _dependenciesTanhGenerated = requireDependenciesTanh_generated();
		var _dependenciesTauGenerated = requireDependenciesTau_generated();
		var _dependenciesThomsonCrossSectionGenerated = requireDependenciesThomsonCrossSection_generated();
		var _dependenciesToGenerated = requireDependenciesTo_generated();
		var _dependenciesToBestGenerated = requireDependenciesToBest_generated();
		var _dependenciesTraceGenerated = requireDependenciesTrace_generated();
		var _dependenciesTransposeGenerated = requireDependenciesTranspose_generated();
		var _dependenciesTrueGenerated = requireDependenciesTrue_generated();
		var _dependenciesTypeOfGenerated = requireDependenciesTypeOf_generated();
		var _dependenciesTypedGenerated = requireDependenciesTyped_generated();
		var _dependenciesUnaryMinusGenerated = requireDependenciesUnaryMinus_generated();
		var _dependenciesUnaryPlusGenerated = requireDependenciesUnaryPlus_generated();
		var _dependenciesUnequalGenerated = requireDependenciesUnequal_generated();
		var _dependenciesUnitClassGenerated = requireDependenciesUnitClass_generated();
		var _dependenciesUnitFunctionGenerated = requireDependenciesUnitFunction_generated();
		var _dependenciesUppercaseEGenerated = requireDependenciesUppercaseE_generated();
		var _dependenciesUppercasePiGenerated = requireDependenciesUppercasePi_generated();
		var _dependenciesUsolveGenerated = requireDependenciesUsolve_generated();
		var _dependenciesUsolveAllGenerated = requireDependenciesUsolveAll_generated();
		var _dependenciesVacuumImpedanceGenerated = requireDependenciesVacuumImpedance_generated();
		var _dependenciesVarianceGenerated = requireDependenciesVariance_generated();
		var _dependenciesVarianceTransformGenerated = requireDependenciesVarianceTransform_generated();
		var _dependenciesVersionGenerated = requireDependenciesVersion_generated();
		var _dependenciesWeakMixingAngleGenerated = requireDependenciesWeakMixingAngle_generated();
		var _dependenciesWienDisplacementGenerated = requireDependenciesWienDisplacement_generated();
		var _dependenciesXgcdGenerated = requireDependenciesXgcd_generated();
		var _dependenciesXorGenerated = requireDependenciesXor_generated();
		var _dependenciesZerosGenerated = requireDependenciesZeros_generated();
		var _dependenciesZetaGenerated = requireDependenciesZeta_generated();
		var _dependenciesZpk2tfGenerated = requireDependenciesZpk2tf_generated();
		var _allFactoriesAny = requireAllFactoriesAny(); 
	} (dependenciesAny_generated));
	return dependenciesAny_generated;
}

var create = {};

var emitter = {};

var tinyEmitter = {exports: {}};

var hasRequiredTinyEmitter;

function requireTinyEmitter () {
	if (hasRequiredTinyEmitter) return tinyEmitter.exports;
	hasRequiredTinyEmitter = 1;
	function E () {
	  // Keep this empty so it's easier to inherit from
	  // (via https://github.com/lipsmack from https://github.com/scottcorgan/tiny-emitter/issues/3)
	}

	E.prototype = {
	  on: function (name, callback, ctx) {
	    var e = this.e || (this.e = {});

	    (e[name] || (e[name] = [])).push({
	      fn: callback,
	      ctx: ctx
	    });

	    return this;
	  },

	  once: function (name, callback, ctx) {
	    var self = this;
	    function listener () {
	      self.off(name, listener);
	      callback.apply(ctx, arguments);
	    }
	    listener._ = callback;
	    return this.on(name, listener, ctx);
	  },

	  emit: function (name) {
	    var data = [].slice.call(arguments, 1);
	    var evtArr = ((this.e || (this.e = {}))[name] || []).slice();
	    var i = 0;
	    var len = evtArr.length;

	    for (i; i < len; i++) {
	      evtArr[i].fn.apply(evtArr[i].ctx, data);
	    }

	    return this;
	  },

	  off: function (name, callback) {
	    var e = this.e || (this.e = {});
	    var evts = e[name];
	    var liveEvents = [];

	    if (evts && callback) {
	      for (var i = 0, len = evts.length; i < len; i++) {
	        if (evts[i].fn !== callback && evts[i].fn._ !== callback)
	          liveEvents.push(evts[i]);
	      }
	    }

	    // Remove event from queue to prevent memory leak
	    // Suggested by https://github.com/lazd
	    // Ref: https://github.com/scottcorgan/tiny-emitter/commit/c6ebfaa9bc973b33d110a84a307742b7cf94c953#commitcomment-5024910

	    (liveEvents.length)
	      ? e[name] = liveEvents
	      : delete e[name];

	    return this;
	  }
	};

	tinyEmitter.exports = E;
	tinyEmitter.exports.TinyEmitter = E;
	return tinyEmitter.exports;
}

var hasRequiredEmitter;

function requireEmitter () {
	if (hasRequiredEmitter) return emitter;
	hasRequiredEmitter = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(emitter, "__esModule", {
	  value: true
	});
	emitter.mixin = mixin;
	var _tinyEmitter = _interopRequireDefault(requireTinyEmitter());
	/**
	 * Extend given object with emitter functions `on`, `off`, `once`, `emit`
	 * @param {Object} obj
	 * @return {Object} obj
	 */
	function mixin(obj) {
	  // create event emitter
	  const emitter = new _tinyEmitter.default();

	  // bind methods to obj (we don't want to expose the emitter.e Array...)
	  obj.on = emitter.on.bind(emitter);
	  obj.off = emitter.off.bind(emitter);
	  obj.once = emitter.once.bind(emitter);
	  obj.emit = emitter.emit.bind(emitter);
	  return obj;
	}
	return emitter;
}

var _import = {};

var hasRequired_import;

function require_import () {
	if (hasRequired_import) return _import;
	hasRequired_import = 1;

	Object.defineProperty(_import, "__esModule", {
	  value: true
	});
	_import.importFactory = importFactory;
	var _is = requireIs();
	var _factory = requireFactory$b();
	var _object = requireObject$3();
	var _ArgumentsError = requireArgumentsError();
	function importFactory(typed, load, math, importedFactories) {
	  /**
	   * Import functions from an object or a module.
	   *
	   * This function is only available on a mathjs instance created using `create`.
	   *
	   * Syntax:
	   *
	   *    math.import(functions)
	   *    math.import(functions, options)
	   *
	   * Where:
	   *
	   * - `functions: Object`
	   *   An object with functions or factories to be imported.
	   * - `options: Object` An object with import options. Available options:
	   *   - `override: boolean`
	   *     If true, existing functions will be overwritten. False by default.
	   *   - `silent: boolean`
	   *     If true, the function will not throw errors on duplicates or invalid
	   *     types. False by default.
	   *   - `wrap: boolean`
	   *     If true, the functions will be wrapped in a wrapper function
	   *     which converts data types like Matrix to primitive data types like Array.
	   *     The wrapper is needed when extending math.js with libraries which do not
	   *     support these data type. False by default.
	   *
	   * Examples:
	   *
	   *    import { create, all } from 'mathjs'
	   *    import * as numbers from 'numbers'
	   *
	   *    // create a mathjs instance
	   *    const math = create(all)
	   *
	   *    // define new functions and variables
	   *    math.import({
	   *      myvalue: 42,
	   *      hello: function (name) {
	   *        return 'hello, ' + name + '!'
	   *      }
	   *    })
	   *
	   *    // use the imported function and variable
	   *    math.myvalue * 2               // 84
	   *    math.hello('user')             // 'hello, user!'
	   *
	   *    // import the npm module 'numbers'
	   *    // (must be installed first with `npm install numbers`)
	   *    math.import(numbers, {wrap: true})
	   *
	   *    math.fibonacci(7) // returns 13
	   *
	   * History:
	   *
	   *    v0.2   Created
	   *    v0.7   Changed second parameter to an options object
	   *    v2     Dropped support for direct import of a module by name
	   *    v14.2  Add facility for specifying the former name of an import
	   *
	   * @param {Object | Array} functions  Object with functions to be imported.
	   * @param {Object} [options]          Import options.
	   */
	  function mathImport(functions, options) {
	    const num = arguments.length;
	    if (num !== 1 && num !== 2) {
	      throw new _ArgumentsError.ArgumentsError('import', num, 1, 2);
	    }
	    if (!options) {
	      options = {};
	    }
	    function flattenImports(flatValues, value, name) {
	      if (Array.isArray(value)) {
	        value.forEach(item => flattenImports(flatValues, item));
	      } else if ((0, _is.isObject)(value) || isModule(value)) {
	        for (const name in value) {
	          if ((0, _object.hasOwnProperty)(value, name)) {
	            flattenImports(flatValues, value[name], name);
	          }
	        }
	      } else if ((0, _factory.isFactory)(value) || name !== undefined) {
	        const flatName = (0, _factory.isFactory)(value) ? isTransformFunctionFactory(value) ? value.fn + '.transform' // TODO: this is ugly
	        : value.fn : name;

	        // we allow importing the same function twice if it points to the same implementation
	        if ((0, _object.hasOwnProperty)(flatValues, flatName) && flatValues[flatName] !== value && !options.silent) {
	          throw new Error('Cannot import "' + flatName + '" twice');
	        }
	        flatValues[flatName] = value;
	      } else {
	        if (!options.silent) {
	          throw new TypeError('Factory, Object, or Array expected');
	        }
	      }
	    }
	    const flatValues = {};
	    flattenImports(flatValues, functions);
	    for (const name in flatValues) {
	      if ((0, _object.hasOwnProperty)(flatValues, name)) {
	        // console.log('import', name)
	        const value = flatValues[name];
	        if ((0, _factory.isFactory)(value)) {
	          // we ignore name here and enforce the name of the factory
	          // maybe at some point we do want to allow overriding it
	          // in that case we can implement an option overrideFactoryNames: true
	          _importFactory(value, options);
	        } else if (isSupportedType(value)) {
	          _import(name, value, options);
	        } else {
	          if (!options.silent) {
	            throw new TypeError('Factory, Object, or Array expected');
	          }
	        }
	      }
	    }
	  }

	  /**
	   * Add a property to the math namespace
	   * @param {string} name
	   * @param {*} value
	   * @param {Object} options  See import for a description of the options
	   * @private
	   */
	  function _import(name, value, options) {
	    var _math$Unit;
	    // TODO: refactor this function, it's to complicated and contains duplicate code
	    if (options.wrap && typeof value === 'function') {
	      // create a wrapper around the function
	      value = _wrap(value);
	    }

	    // turn a plain function with a typed-function signature into a typed-function
	    if (hasTypedFunctionSignature(value)) {
	      value = typed(name, {
	        [value.signature]: value
	      });
	    }
	    if (typed.isTypedFunction(math[name]) && typed.isTypedFunction(value)) {
	      if (options.override) {
	        // give the typed function the right name
	        value = typed(name, value.signatures);
	      } else {
	        // merge the existing and typed function
	        value = typed(math[name], value);
	      }
	      math[name] = value;
	      delete importedFactories[name];
	      _importTransform(name, value);
	      math.emit('import', name, function resolver() {
	        return value;
	      });
	      return;
	    }
	    const isDefined = math[name] !== undefined;
	    const isValuelessUnit = (_math$Unit = math.Unit) === null || _math$Unit === void 0 ? void 0 : _math$Unit.isValuelessUnit(name);
	    if (!isDefined && !isValuelessUnit || options.override) {
	      math[name] = value;
	      delete importedFactories[name];
	      _importTransform(name, value);
	      math.emit('import', name, function resolver() {
	        return value;
	      });
	      return;
	    }
	    if (!options.silent) {
	      throw new Error('Cannot import "' + name + '": already exists');
	    }
	  }
	  function _importTransform(name, value) {
	    if (value && typeof value.transform === 'function') {
	      math.expression.transform[name] = value.transform;
	      if (allowedInExpressions(name)) {
	        math.expression.mathWithTransform[name] = value.transform;
	      }
	    } else {
	      // remove existing transform
	      delete math.expression.transform[name];
	      if (allowedInExpressions(name)) {
	        math.expression.mathWithTransform[name] = value;
	      }
	    }
	  }
	  function _deleteTransform(name) {
	    delete math.expression.transform[name];
	    if (allowedInExpressions(name)) {
	      math.expression.mathWithTransform[name] = math[name];
	    } else {
	      delete math.expression.mathWithTransform[name];
	    }
	  }

	  /**
	   * Create a wrapper a round an function which converts the arguments
	   * to their primitive values (like convert a Matrix to Array)
	   * @param {Function} fn
	   * @return {Function} Returns the wrapped function
	   * @private
	   */
	  function _wrap(fn) {
	    const wrapper = function wrapper() {
	      const args = [];
	      for (let i = 0, len = arguments.length; i < len; i++) {
	        const arg = arguments[i];
	        args[i] = arg && arg.valueOf();
	      }
	      return fn.apply(math, args);
	    };
	    if (fn.transform) {
	      wrapper.transform = fn.transform;
	    }
	    return wrapper;
	  }

	  /**
	   * Import an instance of a factory into math.js
	   * @param {function(scope: object)} factory
	   * @param {Object} options  See import for a description of the options
	   * @param {string} [name=factory.name] Optional custom name
	   * @private
	   */
	  function _importFactory(factory, options) {
	    var _factory$meta$formerl, _factory$meta;
	    let name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : factory.fn;
	    if (name.includes('.')) {
	      throw new Error('Factory name should not contain a nested path. ' + 'Name: ' + JSON.stringify(name));
	    }
	    const namespace = isTransformFunctionFactory(factory) ? math.expression.transform : math;
	    const existingTransform = name in math.expression.transform;
	    const existing = (0, _object.hasOwnProperty)(namespace, name) ? namespace[name] : undefined;
	    const resolver = function () {
	      // collect all dependencies, handle finding both functions and classes and other special cases
	      const dependencies = {};
	      factory.dependencies.map(_factory.stripOptionalNotation).forEach(dependency => {
	        if (dependency.includes('.')) {
	          throw new Error('Factory dependency should not contain a nested path. ' + 'Name: ' + JSON.stringify(dependency));
	        }
	        if (dependency === 'math') {
	          dependencies.math = math;
	        } else if (dependency === 'mathWithTransform') {
	          dependencies.mathWithTransform = math.expression.mathWithTransform;
	        } else if (dependency === 'classes') {
	          // special case for json reviver
	          dependencies.classes = math;
	        } else {
	          dependencies[dependency] = math[dependency];
	        }
	      });
	      const instance = /* #__PURE__ */factory(dependencies);
	      if (instance && typeof instance.transform === 'function') {
	        throw new Error('Transforms cannot be attached to factory functions. ' + 'Please create a separate function for it with export const path = "expression.transform"');
	      }
	      if (existing === undefined || options.override) {
	        return instance;
	      }
	      if (typed.isTypedFunction(existing) && typed.isTypedFunction(instance)) {
	        // merge the existing and new typed function
	        return typed(existing, instance);
	      }
	      if (options.silent) {
	        // keep existing, ignore imported function
	        return existing;
	      } else {
	        throw new Error('Cannot import "' + name + '": already exists');
	      }
	    };
	    const former = (_factory$meta$formerl = (_factory$meta = factory.meta) === null || _factory$meta === void 0 ? void 0 : _factory$meta.formerly) !== null && _factory$meta$formerl !== void 0 ? _factory$meta$formerl : '';
	    const needsTransform = isTransformFunctionFactory(factory) || factoryAllowedInExpressions(factory);
	    const withTransform = math.expression.mathWithTransform;

	    // TODO: add unit test with non-lazy factory
	    if (!factory.meta || factory.meta.lazy !== false) {
	      (0, _object.lazy)(namespace, name, resolver);
	      if (former) (0, _object.lazy)(namespace, former, resolver);

	      // FIXME: remove the `if (existing &&` condition again. Can we make sure subset is loaded before subset.transform? (Name collision, and no dependencies between the two)
	      if (existing && existingTransform) {
	        _deleteTransform(name);
	        if (former) _deleteTransform(former);
	      } else {
	        if (needsTransform) {
	          (0, _object.lazy)(withTransform, name, () => namespace[name]);
	          if (former) (0, _object.lazy)(withTransform, former, () => namespace[name]);
	        }
	      }
	    } else {
	      namespace[name] = resolver();
	      if (former) namespace[former] = namespace[name];

	      // FIXME: remove the `if (existing &&` condition again. Can we make sure subset is loaded before subset.transform? (Name collision, and no dependencies between the two)
	      if (existing && existingTransform) {
	        _deleteTransform(name);
	        if (former) _deleteTransform(former);
	      } else {
	        if (needsTransform) {
	          (0, _object.lazy)(withTransform, name, () => namespace[name]);
	          if (former) (0, _object.lazy)(withTransform, former, () => namespace[name]);
	        }
	      }
	    }

	    // TODO: improve factories, store a list with imports instead which can be re-played
	    importedFactories[name] = factory;
	    math.emit('import', name, resolver);
	  }

	  /**
	   * Check whether given object is a type which can be imported
	   * @param {Function | number | string | boolean | null | Unit | Complex} object
	   * @return {boolean}
	   * @private
	   */
	  function isSupportedType(object) {
	    return typeof object === 'function' || typeof object === 'number' || typeof object === 'string' || typeof object === 'boolean' || object === null || (0, _is.isUnit)(object) || (0, _is.isComplex)(object) || (0, _is.isBigNumber)(object) || (0, _is.isFraction)(object) || (0, _is.isMatrix)(object) || Array.isArray(object);
	  }
	  function isModule(object) {
	    return typeof object === 'object' && object[Symbol.toStringTag] === 'Module';
	  }
	  function hasTypedFunctionSignature(fn) {
	    return typeof fn === 'function' && typeof fn.signature === 'string';
	  }
	  function allowedInExpressions(name) {
	    return !(0, _object.hasOwnProperty)(unsafe, name);
	  }
	  function factoryAllowedInExpressions(factory) {
	    return !factory.fn.includes('.') &&
	    // FIXME: make checking on path redundant, check on meta data instead
	    !(0, _object.hasOwnProperty)(unsafe, factory.fn) && (!factory.meta || !factory.meta.isClass);
	  }
	  function isTransformFunctionFactory(factory) {
	    return factory !== undefined && factory.meta !== undefined && factory.meta.isTransformFunction === true || false;
	  }

	  // namespaces and functions not available in the parser for safety reasons
	  const unsafe = {
	    expression: true,
	    type: true,
	    docs: true,
	    error: true,
	    json: true,
	    chain: true // chain method not supported. Note that there is a unit chain too.
	  };
	  return mathImport;
	}
	return _import;
}

var hasRequiredCreate;

function requireCreate () {
	if (hasRequiredCreate) return create;
	hasRequiredCreate = 1;

	var _interopRequireDefault = requireInteropRequireDefault();
	Object.defineProperty(create, "__esModule", {
	  value: true
	});
	create.create = create$1;
	var _extends2 = _interopRequireDefault(require_extends());
	var _typedFunction = _interopRequireDefault(require$$2$1);
	var _ArgumentsError = requireArgumentsError();
	var _DimensionError = requireDimensionError();
	var _IndexError = requireIndexError();
	var _factory = requireFactory$b();
	var _is = requireIs();
	var _object = requireObject$3();
	var emitter = _interopRequireWildcard(requireEmitter());
	var _config = requireConfig$2();
	var _config2 = requireConfig$1();
	var _import = require_import();
	function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
	/**
	 * Create a mathjs instance from given factory functions and optionally config
	 *
	 * Usage:
	 *
	 *     const mathjs1 = create({ createAdd, createMultiply, ...})
	 *     const config = { number: 'BigNumber' }
	 *     const mathjs2 = create(all, config)
	 *
	 * @param {Object} [factories] An object with factory functions
	 *                             The object can contain nested objects,
	 *                             all nested objects will be flattened.
	 * @param {Object} [config]    Available options:
	 *                            {number} relTol
	 *                              Minimum relative difference between two
	 *                              compared values, used by all comparison functions.
	 *                            {number} absTol
	 *                              Minimum absolute difference between two
	 *                              compared values, used by all comparison functions.
	 *                            {string} matrix
	 *                              A string 'Matrix' (default) or 'Array'.
	 *                            {string} number
	 *                              A string 'number' (default), 'BigNumber', or 'Fraction'
	 *                            {number} precision
	 *                              The number of significant digits for BigNumbers.
	 *                              Not applicable for Numbers.
	 *                            {boolean} predictable
	 *                              Predictable output type of functions. When true,
	 *                              output type depends only on the input types. When
	 *                              false (default), output type can vary depending
	 *                              on input values. For example `math.sqrt(-4)`
	 *                              returns `complex('2i')` when predictable is false, and
	 *                              returns `NaN` when true.
	 *                            {string} randomSeed
	 *                              Random seed for seeded pseudo random number generator.
	 *                              Set to null to randomly seed.
	 * @returns {Object} Returns a bare-bone math.js instance containing
	 *                   functions:
	 *                   - `import` to add new functions
	 *                   - `config` to change configuration
	 *                   - `on`, `off`, `once`, `emit` for events
	 */
	function create$1(factories, config) {
	  const configInternal = (0, _extends2.default)({}, _config.DEFAULT_CONFIG, config);

	  // simple test for ES5 support
	  if (typeof Object.create !== 'function') {
	    throw new Error('ES5 not supported by this JavaScript engine. ' + 'Please load the es5-shim and es5-sham library for compatibility.');
	  }

	  // create the mathjs instance
	  const math = emitter.mixin({
	    // only here for backward compatibility for legacy factory functions
	    isNumber: _is.isNumber,
	    isComplex: _is.isComplex,
	    isBigNumber: _is.isBigNumber,
	    isBigInt: _is.isBigInt,
	    isFraction: _is.isFraction,
	    isUnit: _is.isUnit,
	    isString: _is.isString,
	    isArray: _is.isArray,
	    isMatrix: _is.isMatrix,
	    isCollection: _is.isCollection,
	    isDenseMatrix: _is.isDenseMatrix,
	    isSparseMatrix: _is.isSparseMatrix,
	    isRange: _is.isRange,
	    isIndex: _is.isIndex,
	    isBoolean: _is.isBoolean,
	    isResultSet: _is.isResultSet,
	    isHelp: _is.isHelp,
	    isFunction: _is.isFunction,
	    isDate: _is.isDate,
	    isRegExp: _is.isRegExp,
	    isObject: _is.isObject,
	    isMap: _is.isMap,
	    isPartitionedMap: _is.isPartitionedMap,
	    isObjectWrappingMap: _is.isObjectWrappingMap,
	    isNull: _is.isNull,
	    isUndefined: _is.isUndefined,
	    isAccessorNode: _is.isAccessorNode,
	    isArrayNode: _is.isArrayNode,
	    isAssignmentNode: _is.isAssignmentNode,
	    isBlockNode: _is.isBlockNode,
	    isConditionalNode: _is.isConditionalNode,
	    isConstantNode: _is.isConstantNode,
	    isFunctionAssignmentNode: _is.isFunctionAssignmentNode,
	    isFunctionNode: _is.isFunctionNode,
	    isIndexNode: _is.isIndexNode,
	    isNode: _is.isNode,
	    isObjectNode: _is.isObjectNode,
	    isOperatorNode: _is.isOperatorNode,
	    isParenthesisNode: _is.isParenthesisNode,
	    isRangeNode: _is.isRangeNode,
	    isRelationalNode: _is.isRelationalNode,
	    isSymbolNode: _is.isSymbolNode,
	    isChain: _is.isChain
	  });

	  // load config function and apply provided config
	  math.config = (0, _config2.configFactory)(configInternal, math.emit);
	  math.expression = {
	    transform: {},
	    mathWithTransform: {
	      config: math.config
	    }
	  };

	  // cached factories and instances used by function load
	  const legacyFactories = [];
	  const legacyInstances = [];

	  /**
	   * Load a function or data type from a factory.
	   * If the function or data type already exists, the existing instance is
	   * returned.
	   * @param {Function} factory
	   * @returns {*}
	   */
	  function load(factory) {
	    if ((0, _factory.isFactory)(factory)) {
	      return factory(math);
	    }
	    const firstProperty = factory[Object.keys(factory)[0]];
	    if ((0, _factory.isFactory)(firstProperty)) {
	      return firstProperty(math);
	    }
	    if (!(0, _object.isLegacyFactory)(factory)) {
	      console.warn('Factory object with properties `type`, `name`, and `factory` expected', factory);
	      throw new Error('Factory object with properties `type`, `name`, and `factory` expected');
	    }
	    const index = legacyFactories.indexOf(factory);
	    let instance;
	    if (index === -1) {
	      // doesn't yet exist
	      if (factory.math === true) {
	        // pass with math namespace
	        instance = factory.factory(math.type, configInternal, load, math.typed, math);
	      } else {
	        instance = factory.factory(math.type, configInternal, load, math.typed);
	      }

	      // append to the cache
	      legacyFactories.push(factory);
	      legacyInstances.push(instance);
	    } else {
	      // already existing function, return the cached instance
	      instance = legacyInstances[index];
	    }
	    return instance;
	  }
	  const importedFactories = {};

	  // load the import function
	  function lazyTyped() {
	    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
	      args[_key] = arguments[_key];
	    }
	    return math.typed.apply(math.typed, args);
	  }
	  lazyTyped.isTypedFunction = _typedFunction.default.isTypedFunction;
	  const internalImport = (0, _import.importFactory)(lazyTyped, load, math, importedFactories);
	  math.import = internalImport;

	  // listen for changes in config, import all functions again when changed
	  // TODO: move this listener into the import function?
	  math.on('config', () => {
	    Object.values(importedFactories).forEach(factory => {
	      if (factory && factory.meta && factory.meta.recreateOnConfigChange) {
	        // FIXME: only re-create when the current instance is the same as was initially created
	        // FIXME: delete the functions/constants before importing them again?
	        internalImport(factory, {
	          override: true
	        });
	      }
	    });
	  });

	  // the create function exposed on the mathjs instance is bound to
	  // the factory functions passed before
	  math.create = create$1.bind(null, factories);

	  // export factory function
	  math.factory = _factory.factory;

	  // import the factory functions like createAdd as an array instead of object,
	  // else they will get a different naming (`createAdd` instead of `add`).
	  math.import(Object.values((0, _object.deepFlatten)(factories)));
	  math.ArgumentsError = _ArgumentsError.ArgumentsError;
	  math.DimensionError = _DimensionError.DimensionError;
	  math.IndexError = _IndexError.IndexError;
	  return math;
	}
	return create;
}

var hasRequiredMainAny;

function requireMainAny () {
	if (hasRequiredMainAny) return mainAny;
	hasRequiredMainAny = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		var _exportNames = {
		  config: true,
		  IndexError: true,
		  DimensionError: true,
		  ArgumentsError: true,
		  create: true,
		  factory: true
		};
		Object.defineProperty(exports, "ArgumentsError", {
		  enumerable: true,
		  get: function () {
		    return _ArgumentsError.ArgumentsError;
		  }
		});
		Object.defineProperty(exports, "DimensionError", {
		  enumerable: true,
		  get: function () {
		    return _DimensionError.DimensionError;
		  }
		});
		Object.defineProperty(exports, "IndexError", {
		  enumerable: true,
		  get: function () {
		    return _IndexError.IndexError;
		  }
		});
		Object.defineProperty(exports, "config", {
		  enumerable: true,
		  get: function () {
		    return _configReadonly.config;
		  }
		});
		Object.defineProperty(exports, "create", {
		  enumerable: true,
		  get: function () {
		    return _create.create;
		  }
		});
		Object.defineProperty(exports, "factory", {
		  enumerable: true,
		  get: function () {
		    return _factory.factory;
		  }
		});
		var _configReadonly = requireConfigReadonly();
		var _pureFunctionsAnyGenerated = requirePureFunctionsAny_generated();
		Object.keys(_pureFunctionsAnyGenerated).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
		  if (key in exports && exports[key] === _pureFunctionsAnyGenerated[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _pureFunctionsAnyGenerated[key];
		    }
		  });
		});
		var _impureFunctionsAnyGenerated = requireImpureFunctionsAny_generated();
		Object.keys(_impureFunctionsAnyGenerated).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
		  if (key in exports && exports[key] === _impureFunctionsAnyGenerated[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _impureFunctionsAnyGenerated[key];
		    }
		  });
		});
		var _typeChecks = requireTypeChecks();
		Object.keys(_typeChecks).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
		  if (key in exports && exports[key] === _typeChecks[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _typeChecks[key];
		    }
		  });
		});
		var _IndexError = requireIndexError();
		var _DimensionError = requireDimensionError();
		var _ArgumentsError = requireArgumentsError();
		var _dependenciesAnyGenerated = requireDependenciesAny_generated();
		Object.keys(_dependenciesAnyGenerated).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
		  if (key in exports && exports[key] === _dependenciesAnyGenerated[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _dependenciesAnyGenerated[key];
		    }
		  });
		});
		var _factoriesAny = requireFactoriesAny();
		Object.keys(_factoriesAny).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
		  if (key in exports && exports[key] === _factoriesAny[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _factoriesAny[key];
		    }
		  });
		});
		var _create = requireCreate();
		var _factory = requireFactory$b(); 
	} (mainAny));
	return mainAny;
}

var hasRequiredCjs;

function requireCjs () {
	if (hasRequiredCjs) return cjs;
	hasRequiredCjs = 1;
	(function (exports) {

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		var _mainAny = requireMainAny();
		Object.keys(_mainAny).forEach(function (key) {
		  if (key === "default" || key === "__esModule") return;
		  if (key in exports && exports[key] === _mainAny[key]) return;
		  Object.defineProperty(exports, key, {
		    enumerable: true,
		    get: function () {
		      return _mainAny[key];
		    }
		  });
		}); 
	} (cjs));
	return cjs;
}

var ComponentValidationResult = {};

var hasRequiredComponentValidationResult;

function requireComponentValidationResult () {
	if (hasRequiredComponentValidationResult) return ComponentValidationResult;
	hasRequiredComponentValidationResult = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentValidationResultSchema = exports.ComponentValidationResultItemSchema = exports.ComponentValidationErrorType = void 0;
		exports.isExpressionValidationResultItem = isExpressionValidationResultItem;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryLevel_1 = requireBoundaryLevel();
		var ComponentValidationErrorType;
		(function (ComponentValidationErrorType) {
		    ComponentValidationErrorType["ModelResolutionError"] = "ModelResolutionError";
		    ComponentValidationErrorType["ModelValidationError"] = "ModelValidationError";
		    ComponentValidationErrorType["UnmappedSignals"] = "UnmappedSignals";
		    ComponentValidationErrorType["ModeValidationError"] = "ModeValidationError";
		    ComponentValidationErrorType["BoundaryValidationError"] = "BoundaryValidationError";
		    ComponentValidationErrorType["ResourceValidationError"] = "ResourceValidationError";
		    ComponentValidationErrorType["FunctionValidationError"] = "FunctionValidationError";
		    ComponentValidationErrorType["ModeCircularReferenceError"] = "ModeCircularReferenceError";
		    ComponentValidationErrorType["ParameterValidationError"] = "ParameterValidationError";
		    ComponentValidationErrorType["InternalConsistencyError"] = "InternalConsistencyError";
		    ComponentValidationErrorType["SpiceTestRunError"] = "SpiceTestRunError";
		    ComponentValidationErrorType["ExpressionValidationError"] = "ExpressionValidationError";
		    ComponentValidationErrorType["GenericError"] = "GenericError";
		})(ComponentValidationErrorType || (exports.ComponentValidationErrorType = ComponentValidationErrorType = {}));
		exports.ComponentValidationResultItemSchema = zod_1.z
		    .object({
		    type: zod_1.z.nativeEnum(ComponentValidationErrorType),
		    message: zod_1.z.string(),
		    severity: BoundaryLevel_1.BoundaryLevelSchema,
		    innerResult: zod_1.z.any().optional(),
		})
		    .catchall(zod_1.z.any());
		exports.ComponentValidationResultSchema = zod_1.z.object({
		    isValid: zod_1.z.boolean(),
		    errors: exports.ComponentValidationResultItemSchema.array().optional(),
		});
		function isExpressionValidationResultItem(item) {
		    return item?.type === ComponentValidationErrorType.ExpressionValidationError;
		}
		
	} (ComponentValidationResult));
	return ComponentValidationResult;
}

var Errors$1 = {};

var hasRequiredErrors$1;

function requireErrors$1 () {
	if (hasRequiredErrors$1) return Errors$1;
	hasRequiredErrors$1 = 1;
	Object.defineProperty(Errors$1, "__esModule", { value: true });
	Errors$1.IObjectWithSeverity = Errors$1.AggregateErrorWithSeverity = Errors$1.ComponentInstanceValidationError = Errors$1.ComponentModelValidationError = Errors$1.ErrorWithInternal = void 0;
	Errors$1.serializeInnerError = serializeInnerError;
	Errors$1.deserializeInnerError = deserializeInnerError;
	Errors$1.isErrorWithSeverity = isErrorWithSeverity;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryLevel_1 = requireBoundaryLevel();
	function serializeInnerError(error) {
	    if ('toJSON' in error && typeof error.toJSON === 'function') {
	        return error.toJSON();
	    }
	    const result = {
	        message: error.message,
	        stack: error.stack,
	        name: error.name,
	    };
	    if ('errors' in error && Array.isArray(error.errors)) {
	        result.errors = error.errors.map((e) => (e instanceof Error ? serializeInnerError(e) : e));
	    }
	    return result;
	}
	function deserializeInnerError(serialized) {
	    const message = typeof serialized.message === 'string' ? serialized.message : '';
	    const name = typeof serialized.name === 'string' ? serialized.name : undefined;
	    const stack = typeof serialized.stack === 'string' ? serialized.stack : undefined;
	    const rawErrors = Array.isArray(serialized.errors) ? serialized.errors : undefined;
	    const rehydratedErrors = rawErrors?.map((e) => e !== null && typeof e === 'object' ?
	        deserializeInnerError(e)
	        : new Error(typeof e === 'string' ? e : String(e)));
	    const error = rehydratedErrors !== undefined ? new AggregateError(rehydratedErrors, message) : new Error(message);
	    if (name !== undefined) {
	        error.name = name;
	        Object.defineProperty(error, 'name', { value: name });
	    }
	    if (stack !== undefined) {
	        error.stack = stack;
	        Object.defineProperty(error, 'stack', { value: stack });
	    }
	    if (serialized.innerError && typeof serialized.innerError === 'object') {
	        error.innerError = deserializeInnerError(serialized.innerError);
	    }
	    return error;
	}
	class ErrorWithInternal extends Error {
	    constructor(message, innerError) {
	        super(message);
	        this.innerError = innerError;
	        Object.setPrototypeOf(this, ErrorWithInternal.prototype);
	    }
	    toJSON() {
	        return {
	            message: this.message,
	            innerError: this.innerError ? serializeInnerError(this.innerError) : undefined,
	            stack: this.stack,
	            name: this.name,
	        };
	    }
	}
	Errors$1.ErrorWithInternal = ErrorWithInternal;
	class ComponentModelValidationError extends ErrorWithInternal {
	    constructor(type, message, severity = BoundaryLevel_1.BoundaryLevel.Error, modelId, innerError) {
	        super(`Failure during component model validation: ${message}`, innerError);
	        this.type = type;
	        this.severity = severity;
	        this.modelId = modelId;
	        Object.setPrototypeOf(this, ComponentModelValidationError.prototype);
	    }
	}
	Errors$1.ComponentModelValidationError = ComponentModelValidationError;
	class ComponentInstanceValidationError extends ErrorWithInternal {
	    constructor(type, message, severity = BoundaryLevel_1.BoundaryLevel.Error, innerError) {
	        super(`Failure during component instance validation: ${message}`, innerError);
	        this.type = type;
	        this.severity = severity;
	        Object.setPrototypeOf(this, ComponentInstanceValidationError.prototype);
	    }
	}
	Errors$1.ComponentInstanceValidationError = ComponentInstanceValidationError;
	class AggregateErrorWithSeverity extends AggregateError {
	    constructor(errors, message = 'One or more errors occurred', severityOverride, defaultSeverity = BoundaryLevel_1.BoundaryLevel.Error) {
	        const severity = severityOverride ??
	            (0, BoundaryLevel_1.getHighestBoundaryLevelFromArray)(Array.from(errors).map((err) => (isErrorWithSeverity(err) ? err.severity : undefined)), defaultSeverity);
	        super(errors, message);
	        Object.setPrototypeOf(this, AggregateErrorWithSeverity.prototype);
	        this.severity = severity;
	    }
	}
	Errors$1.AggregateErrorWithSeverity = AggregateErrorWithSeverity;
	Errors$1.IObjectWithSeverity = zod_1.default.object({
	    severity: BoundaryLevel_1.BoundaryLevelSchema,
	});
	function isErrorWithSeverity(error) {
	    return (error !== null &&
	        typeof error === 'object' &&
	        'severity' in error &&
	        BoundaryLevel_1.BoundaryLevelSchema.safeParse(error.severity).success);
	}
	
	return Errors$1;
}

var hasRequiredCharacteristicExpressionParser;

function requireCharacteristicExpressionParser () {
	if (hasRequiredCharacteristicExpressionParser) return CharacteristicExpressionParser;
	hasRequiredCharacteristicExpressionParser = 1;
	Object.defineProperty(CharacteristicExpressionParser, "__esModule", { value: true });
	CharacteristicExpressionParser.CharacteristicExpressionParser = void 0;
	const mathjs = /*@__PURE__*/ requireCjs();
	const CommonParameterUnits_1 = requireCommonParameterUnits();
	const DesignModels_1 = requireDesignModels();
	const ComponentValidationResult_1 = requireComponentValidationResult();
	const Errors_1 = requireErrors$1();
	const __1 = requireDist$1();
	class CharacteristicExpressionParser$1 {
	    static validateComponentExpression(componentData, expression, characteristic) {
	        try {
	            const executableExpression = this.parseComponentExpression(componentData, expression, characteristic);
	            const context = Object.fromEntries(executableExpression.variables.map((v) => [v.id, 1]));
	            const result = executableExpression.evaluate(context);
	            if (Number.isNaN(result) || !Number.isFinite(result)) {
	                return [
	                    this.transformError(new Error(`Expression evaluates to NaN or Infinity: ${expression}`), expression, characteristic),
	                ];
	            }
	            return [];
	        }
	        catch (err) {
	            return [this.transformError(err, expression, characteristic)];
	        }
	    }
	    static parseComponentExpression(componentOrSignals, expression, characteristic, fixFloatingPointErrors = false, throwOnMissingSignals = true) {
	        const signals = Array.isArray(componentOrSignals) ? componentOrSignals : componentOrSignals.signals;
	        const units = CommonParameterUnits_1.CommonParameterUnits[characteristic];
	        if (!units) {
	            throw new Error(`Unsupported characteristic: ${characteristic}`);
	        }
	        if (expression.trim() === '') {
	            throw new Error('Empty expression');
	        }
	        const parsedSignalsExpression = this.extractSignalVariables(expression, units, signals);
	        expression = parsedSignalsExpression.expression;
	        const parsed = this.parseExpression(expression, characteristic, parsedSignalsExpression.variables, fixFloatingPointErrors);
	        if (throwOnMissingSignals) {
	            const missingVariables = parsed.variables.filter((v) => !v.signal);
	            if (missingVariables.length) {
	                throw new Error(`Missing related signals for variables in expression: ${missingVariables.map((v) => v.id).join(', ')}`);
	            }
	        }
	        return parsed;
	    }
	    static parseExpression(expression, characteristic, parsedVariables = [], fixFloatingPointErrors = false) {
	        const units = CommonParameterUnits_1.CommonParameterUnits[characteristic];
	        if (!units) {
	            throw new Error(`Unsupported characteristic: ${characteristic}`);
	        }
	        const expressionVariables = [...parsedVariables];
	        const normalizedExpr = this.normalizeExpression(expression, units, fixFloatingPointErrors);
	        const variables = this.extractVariables(normalizedExpr);
	        expressionVariables.push(...variables
	            .filter((v) => !parsedVariables?.find((ev) => ev.id === v))
	            .map((v) => ({ id: v, originalName: v, signal: undefined })));
	        return {
	            expression: normalizedExpr,
	            variables: expressionVariables,
	            characteristic,
	            evaluate: (context) => {
	                return mathjs.evaluate(normalizedExpr, context);
	            },
	        };
	    }
	    static tryEvaluateConstant(expression) {
	        if (expression.variables.length > 0) {
	            return undefined;
	        }
	        try {
	            const result = expression.evaluate({});
	            return typeof result === 'number' && Number.isFinite(result) ? result : undefined;
	        }
	        catch {
	            return undefined;
	        }
	    }
	    static evaluateExpressionWithAbsoluteCharacteristicValues(expression, context) {
	        const errors = [];
	        const safeEvaluate = (valueExtractor, fieldName) => {
	            try {
	                const evalContext = Object.fromEntries(Object.entries(context).map(([key, value]) => [key, valueExtractor(value)]));
	                const undefinedKeys = Object.entries(evalContext)
	                    .filter(([_, val]) => __1.Utils.isEmptyValue(val))
	                    .map(([key]) => key);
	                if (undefinedKeys.length > 0) {
	                    return undefined;
	                }
	                return expression.evaluate(evalContext);
	            }
	            catch (error) {
	                const wrappedError = new Errors_1.ErrorWithInternal(`Failed to evaluate ${fieldName} for expression ${expression.expression}`, error instanceof Error ? error : new Error(`Error: ${String(error)}`));
	                errors.push(wrappedError);
	                return undefined;
	            }
	        };
	        return {
	            value: {
	                value: safeEvaluate((value) => value.value, 'nominal value'),
	                allowableRange: {
	                    min: safeEvaluate((value) => value.allowableRange.min, 'minimum value'),
	                    max: safeEvaluate((value) => value.allowableRange.max, 'maximum value'),
	                },
	                discreteValues: [],
	            },
	            errors: errors.length ? errors : undefined,
	        };
	    }
	    static extractVariables(expression) {
	        try {
	            const node = mathjs.parse(expression);
	            const symbols = new Set();
	            node.traverse((node) => {
	                if (node.type === 'SymbolNode') {
	                    symbols.add(node.name);
	                }
	            });
	            return Array.from(symbols);
	        }
	        catch (error) {
	            throw new Errors_1.ErrorWithInternal(`Error parsing expression: "${expression}"`, error);
	        }
	    }
	    static normalizeExpression(expression, units, fixFloatingPointErrors = false) {
	        expression = expression.replace(/ x /g, ' * ');
	        expression = __1.Utils.convertStringExpressionToAscii(expression);
	        expression = this.convertPowerNotationToScientific(expression);
	        const operands = __1.Utils.getOperandsFromExpression(expression);
	        for (const operand of operands) {
	            try {
	                const value = __1.Utils.parseNumericalSIValue(operand, units);
	                if (value !== undefined) {
	                    let valueStr = value.toString();
	                    if (fixFloatingPointErrors) {
	                        valueStr = __1.Utils.correctFloatingPointErrors(value).toString();
	                    }
	                    expression = expression.replaceAll(operand, valueStr);
	                }
	            }
	            catch {
	            }
	        }
	        return expression;
	    }
	    static convertPowerNotationToScientific(expression) {
	        const powerNotationRegex = /(\d+(?:\.\d+)?)\s*\*\s*10\s*\^\s*(-?\d+)/g;
	        return expression.replace(powerNotationRegex, (_match, coefficient, exponent) => {
	            return `${coefficient}e${exponent}`;
	        });
	    }
	    static extractSignalVariables(expression, units, signals) {
	        const expressionVariables = [];
	        const hasCurlyBracedVariables = /\{\{[^}]+\}\}/.test(expression);
	        if (hasCurlyBracedVariables) {
	            const sigidPattern = /\{\{sigid_([^}]+)\}\}/g;
	            let match;
	            while ((match = sigidPattern.exec(expression)) !== null) {
	                const signalId = match[1];
	                const templatePattern = match[0];
	                const signal = signals.find((s) => s.id.toString() === signalId);
	                if (signal) {
	                    let encodedSignalId = signal.id.toString();
	                    if (!/^[a-zA-Z0-9_]+$/.test(encodedSignalId)) {
	                        encodedSignalId = encodedSignalId.replace(/[^a-zA-Z0-9]/g, (match) => '_' + match.charCodeAt(0).toString(36));
	                    }
	                    const newVarName = `sigvar_${encodedSignalId}`;
	                    expression = expression.replace(templatePattern, newVarName);
	                    if (!expressionVariables.find((v) => v.id === newVarName)) {
	                        expressionVariables.push({ id: newVarName, originalName: signal.name, signal });
	                    }
	                }
	                else {
	                    const placeholderVarName = `UNKNOWN_SIGID_${signalId}`;
	                    expression = expression.replace(templatePattern, placeholderVarName);
	                    expressionVariables.push({ id: placeholderVarName, originalName: templatePattern, signal: undefined });
	                }
	            }
	        }
	        else {
	            const sortedSignals = Array.from(signals).sort((a, b) => {
	                const aIncludesB = a.name.includes(b.name);
	                const bIncludesA = b.name.includes(a.name);
	                if (aIncludesB && !bIncludesA) {
	                    return 1;
	                }
	                else if (!aIncludesB && bIncludesA) {
	                    return -1;
	                }
	                else {
	                    return a.name.length - b.name.length;
	                }
	            });
	            for (const signal of sortedSignals) {
	                if (typeof expression.replaceAll !== 'function') {
	                    throw new Error(`Expression ${expression} is not a string`);
	                }
	                if (typeof signal.id !== 'string' ||
	                    /^[^a-zA-Z]$/.test(signal.name) ||
	                    !/^[a-zA-Z]/.test(signal.name) ||
	                    signal.name === units.short ||
	                    signal.name === units.long) {
	                    continue;
	                }
	                expression = expression.replaceAll(new RegExp(`(^|\\W|\\d)(${signal.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})($|\\W|\\d)`, 'ig'), (_substr, prefix, originalName, suffix) => {
	                    let encodedSignalId = signal.id.toString();
	                    if (!/^[a-zA-Z0-9_]+$/.test(encodedSignalId)) {
	                        encodedSignalId = encodedSignalId.replace(/[^a-zA-Z0-9]/g, (match) => '_' + match.charCodeAt(0).toString(36));
	                    }
	                    const newVarName = `sigvar_${encodedSignalId}`;
	                    if (!expressionVariables.find((v) => v.id === newVarName)) {
	                        expressionVariables.push({ id: newVarName, originalName, signal });
	                    }
	                    return `${prefix}${newVarName}${suffix}`;
	                });
	            }
	        }
	        return { expression, variables: expressionVariables };
	    }
	    static transformError(error, expression, characteristic) {
	        const errorMessage = error instanceof Error ? error.message : 'Unknown error during expression validation';
	        const componentModelError = new Errors_1.ComponentModelValidationError(ComponentValidationResult_1.ComponentValidationErrorType.ExpressionValidationError, errorMessage, DesignModels_1.BoundaryLevel.Error, undefined, error instanceof Error ? error : undefined);
	        return {
	            type: ComponentValidationResult_1.ComponentValidationErrorType.ExpressionValidationError,
	            message: componentModelError.message,
	            severity: componentModelError.severity,
	            expression,
	            characteristic: characteristic.toString(),
	        };
	    }
	}
	CharacteristicExpressionParser.CharacteristicExpressionParser = CharacteristicExpressionParser$1;
	
	return CharacteristicExpressionParser;
}

var types$5 = {};

var Claim = {};

var Evidence = {};

var hasRequiredEvidence;

function requireEvidence () {
	if (hasRequiredEvidence) return Evidence;
	hasRequiredEvidence = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EvidenceSchema = exports.AnnotatedFileRegionEvidenceSchema = exports.BoundingBoxSchema = exports.ImageEvidenceSchema = exports.BaseEvidenceSchema = exports.EvidenceTypeSchema = exports.EvidenceType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundingBox_1 = requireBoundingBox();
		var EvidenceType;
		(function (EvidenceType) {
		    EvidenceType["Image"] = "ImageEvidence";
		    EvidenceType["AnnotatedFileRegion"] = "AnnotatedFileRegionEvidence";
		})(EvidenceType || (exports.EvidenceType = EvidenceType = {}));
		exports.EvidenceTypeSchema = zod_1.z.nativeEnum(EvidenceType);
		exports.BaseEvidenceSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    type: exports.EvidenceTypeSchema,
		    description: zod_1.z.string(),
		});
		exports.ImageEvidenceSchema = exports.BaseEvidenceSchema.extend({
		    type: zod_1.z.literal(EvidenceType.Image),
		    image: zod_1.z.string().optional(),
		});
		exports.BoundingBoxSchema = BoundingBox_1.boundingBoxSchema;
		exports.AnnotatedFileRegionEvidenceSchema = exports.BaseEvidenceSchema.extend({
		    type: zod_1.z.literal(EvidenceType.AnnotatedFileRegion),
		    fileId: zod_1.z.string().optional(),
		    pageNumber: zod_1.z.number().optional(),
		    annotations: exports.BoundingBoxSchema.array(),
		    region: exports.BoundingBoxSchema.optional(),
		});
		exports.EvidenceSchema = zod_1.z.discriminatedUnion('type', [
		    exports.ImageEvidenceSchema,
		    exports.AnnotatedFileRegionEvidenceSchema,
		]);
		
	} (Evidence));
	return Evidence;
}

var hasRequiredClaim;

function requireClaim () {
	if (hasRequiredClaim) return Claim;
	hasRequiredClaim = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.UpdateClaimInputSchema = exports.ListClaimInputSchema = exports.CreateClaimInputSchema = exports.ClaimSchema = exports.ClaimStatusSchema = exports.ClaimStatus = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const Evidence_1 = requireEvidence();
		var ClaimStatus;
		(function (ClaimStatus) {
		    ClaimStatus["Draft"] = "draft";
		    ClaimStatus["Reviewed"] = "reviewed";
		})(ClaimStatus || (exports.ClaimStatus = ClaimStatus = {}));
		exports.ClaimStatusSchema = zod_1.z.nativeEnum(ClaimStatus);
		exports.ClaimSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    status: exports.ClaimStatusSchema,
		    claim: zod_1.z.string(),
		    evidence: Evidence_1.EvidenceSchema.array(),
		    reasoning: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		});
		exports.CreateClaimInputSchema = zod_1.z.object({
		    claim: zod_1.z.string(),
		});
		exports.ListClaimInputSchema = zod_1.z.object({
		    status: exports.ClaimStatusSchema.optional(),
		    packageId: zod_1.z.string().optional(),
		});
		exports.UpdateClaimInputSchema = zod_1.z.object({
		    status: exports.ClaimStatusSchema.optional(),
		    claim: zod_1.z.string().optional(),
		    evidence: Evidence_1.EvidenceSchema.array().optional(),
		    reasoning: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		});
		
	} (Claim));
	return Claim;
}

var hasRequiredTypes$5;

function requireTypes$5 () {
	if (hasRequiredTypes$5) return types$5;
	hasRequiredTypes$5 = 1;
	(function (exports) {
		var __createBinding = (types$5 && types$5.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (types$5 && types$5.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireClaim(), exports);
		__exportStar(requireEvidence(), exports);
		
	} (types$5));
	return types$5;
}

var ElectricalSchematic = {};

var INetAnnotation = {};

var NetAnnotationType = {};

var hasRequiredNetAnnotationType;

function requireNetAnnotationType () {
	if (hasRequiredNetAnnotationType) return NetAnnotationType;
	hasRequiredNetAnnotationType = 1;
	Object.defineProperty(NetAnnotationType, "__esModule", { value: true });
	NetAnnotationType.NetAnnotationTypeSchema = NetAnnotationType.NetAnnotationType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var NetAnnotationType$1;
	(function (NetAnnotationType) {
	    NetAnnotationType["Power"] = "power";
	    NetAnnotationType["Ground"] = "ground";
	    NetAnnotationType["Analog"] = "analog";
	    NetAnnotationType["Digital"] = "digital";
	    NetAnnotationType["BusClass"] = "bus-class";
	})(NetAnnotationType$1 || (NetAnnotationType.NetAnnotationType = NetAnnotationType$1 = {}));
	NetAnnotationType.NetAnnotationTypeSchema = zod_1.default.nativeEnum(NetAnnotationType$1);
	
	return NetAnnotationType;
}

var hasRequiredINetAnnotation;

function requireINetAnnotation () {
	if (hasRequiredINetAnnotation) return INetAnnotation;
	hasRequiredINetAnnotation = 1;
	Object.defineProperty(INetAnnotation, "__esModule", { value: true });
	INetAnnotation.INetAnnotationSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const NetAnnotationType_1 = requireNetAnnotationType();
	INetAnnotation.INetAnnotationSchema = zod_1.default
	    .object({
	    net: zod_1.default.string(),
	    type: NetAnnotationType_1.NetAnnotationTypeSchema,
	})
	    .passthrough();
	
	return INetAnnotation;
}

var ISerializedNetAnnotations = {};

var hasRequiredISerializedNetAnnotations;

function requireISerializedNetAnnotations () {
	if (hasRequiredISerializedNetAnnotations) return ISerializedNetAnnotations;
	hasRequiredISerializedNetAnnotations = 1;
	Object.defineProperty(ISerializedNetAnnotations, "__esModule", { value: true });
	ISerializedNetAnnotations.ISerializedNetAnnotationsSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const INetAnnotation_1 = requireINetAnnotation();
	ISerializedNetAnnotations.ISerializedNetAnnotationsSchema = zod_1.default.object({
	    annotations: zod_1.default.record(zod_1.default.string(), zod_1.default.record(zod_1.default.string(), zod_1.default.array(INetAnnotation_1.INetAnnotationSchema))),
	});
	
	return ISerializedNetAnnotations;
}

var NetAnnotationResultSerialized = {};

var hasRequiredNetAnnotationResultSerialized;

function requireNetAnnotationResultSerialized () {
	if (hasRequiredNetAnnotationResultSerialized) return NetAnnotationResultSerialized;
	hasRequiredNetAnnotationResultSerialized = 1;
	Object.defineProperty(NetAnnotationResultSerialized, "__esModule", { value: true });
	NetAnnotationResultSerialized.NetAnnotationResultSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnalysisType_1 = requireAnalysisType$1();
	const ISerializedNetAnnotations_1 = requireISerializedNetAnnotations();
	NetAnnotationResultSerialized.NetAnnotationResultSerializedSchema = zod_1.default.object({
	    type: zod_1.default.literal(AnalysisType_1.AnalysisType.NetAnnotation),
	    result: ISerializedNetAnnotations_1.ISerializedNetAnnotationsSchema,
	});
	
	return NetAnnotationResultSerialized;
}

var NetBusClassAnnotationType = {};

var hasRequiredNetBusClassAnnotationType;

function requireNetBusClassAnnotationType () {
	if (hasRequiredNetBusClassAnnotationType) return NetBusClassAnnotationType;
	hasRequiredNetBusClassAnnotationType = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.INetBusClassAnnotationSchema = exports.INetBusClassAnnotationInferedBusClassSchema = exports.INetBusClassAnnotationInferedBusClassSignalSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const NetAnnotationType_1 = requireNetAnnotationType();
		exports.INetBusClassAnnotationInferedBusClassSignalSchema = zod_1.default.object({
		    classSignalId: zod_1.default.string(),
		    classSignalDisplayName: zod_1.default.string(),
		    confidence: zod_1.default.number(),
		});
		exports.INetBusClassAnnotationInferedBusClassSchema = zod_1.default.object({
		    busClassName: zod_1.default.string(),
		    busClassDisplayName: zod_1.default.string(),
		    confidence: zod_1.default.number(),
		    classSignals: zod_1.default.array(exports.INetBusClassAnnotationInferedBusClassSignalSchema),
		});
		exports.INetBusClassAnnotationSchema = zod_1.default.object({
		    net: zod_1.default.string(),
		    type: zod_1.default.literal(NetAnnotationType_1.NetAnnotationType.BusClass),
		    inferedBusClasses: zod_1.default.array(exports.INetBusClassAnnotationInferedBusClassSchema),
		});
		
	} (NetBusClassAnnotationType));
	return NetBusClassAnnotationType;
}

var NetPowerAnnotationType = {};

var hasRequiredNetPowerAnnotationType;

function requireNetPowerAnnotationType () {
	if (hasRequiredNetPowerAnnotationType) return NetPowerAnnotationType;
	hasRequiredNetPowerAnnotationType = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IGroundAnnotationSchema = exports.IPowerAnnotationSchema = exports.PowerInferenceSourceSchema = exports.IPowerInferenceSourceErcNetTypePowerSchema = exports.IPowerInferenceSourceOutputPowerPinsSchema = exports.IPowerInferenceSourceInputPowerPinsSchema = exports.IPowerInferenceSourceNetNameSchema = exports.IPowerInferenceSourceSupplySymbolSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const DesignModels_1 = requireDesignModels();
		const NetAnnotationType_1 = requireNetAnnotationType();
		exports.IPowerInferenceSourceSupplySymbolSchema = zod_1.default.object({
		    type: zod_1.default.literal('supply-symbol'),
		    id: zod_1.default.string(),
		    value: zod_1.default.number().nullable(),
		});
		exports.IPowerInferenceSourceNetNameSchema = zod_1.default.object({
		    type: zod_1.default.literal('net-name'),
		    value: zod_1.default.number().nullable(),
		});
		exports.IPowerInferenceSourceInputPowerPinsSchema = zod_1.default.object({
		    type: zod_1.default.literal('fed-power-pins'),
		    pins: DesignModels_1.NetConnectionSchema.array(),
		});
		exports.IPowerInferenceSourceOutputPowerPinsSchema = zod_1.default.object({
		    type: zod_1.default.literal('output-power-pins'),
		    pins: DesignModels_1.NetConnectionSchema.array(),
		});
		exports.IPowerInferenceSourceErcNetTypePowerSchema = zod_1.default.object({
		    type: zod_1.default.literal('erc-net-type-power'),
		});
		exports.PowerInferenceSourceSchema = zod_1.default.discriminatedUnion('type', [
		    exports.IPowerInferenceSourceSupplySymbolSchema,
		    exports.IPowerInferenceSourceNetNameSchema,
		    exports.IPowerInferenceSourceInputPowerPinsSchema,
		    exports.IPowerInferenceSourceOutputPowerPinsSchema,
		    exports.IPowerInferenceSourceErcNetTypePowerSchema,
		]);
		exports.IPowerAnnotationSchema = zod_1.default.object({
		    net: zod_1.default.string(),
		    type: zod_1.default.literal(NetAnnotationType_1.NetAnnotationType.Power),
		    inferenceSources: exports.PowerInferenceSourceSchema.array(),
		});
		exports.IGroundAnnotationSchema = zod_1.default.object({
		    net: zod_1.default.string(),
		    type: zod_1.default.literal(NetAnnotationType_1.NetAnnotationType.Ground),
		    inferenceSources: exports.PowerInferenceSourceSchema.array(),
		});
		
	} (NetPowerAnnotationType));
	return NetPowerAnnotationType;
}

var NetSignalAnnotationType = {};

var hasRequiredNetSignalAnnotationType;

function requireNetSignalAnnotationType () {
	if (hasRequiredNetSignalAnnotationType) return NetSignalAnnotationType;
	hasRequiredNetSignalAnnotationType = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IDigitalNetAnnotationSchema = exports.IAnalogNetAnnotationSchema = exports.SignalAttributeInferenceSourceSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const DesignModels_1 = requireDesignModels();
		const NetAnnotationType_1 = requireNetAnnotationType();
		exports.SignalAttributeInferenceSourceSchema = zod_1.default.object({
		    type: zod_1.default.literal('signal-attribute'),
		    module: zod_1.default.string(),
		    signalId: zod_1.default.string(),
		    attribute: DesignModels_1.SignalAttributesSchema.keyof(),
		});
		exports.IAnalogNetAnnotationSchema = zod_1.default.object({
		    net: zod_1.default.string(),
		    type: zod_1.default.literal(NetAnnotationType_1.NetAnnotationType.Analog),
		    inferenceSources: exports.SignalAttributeInferenceSourceSchema.array(),
		});
		exports.IDigitalNetAnnotationSchema = zod_1.default.object({
		    net: zod_1.default.string(),
		    type: zod_1.default.literal(NetAnnotationType_1.NetAnnotationType.Digital),
		    inferenceSources: exports.SignalAttributeInferenceSourceSchema.array(),
		});
		
	} (NetSignalAnnotationType));
	return NetSignalAnnotationType;
}

var NetworkAnalysis = {};

var INetworkAnalysisDependency = {};

var hasRequiredINetworkAnalysisDependency;

function requireINetworkAnalysisDependency () {
	if (hasRequiredINetworkAnalysisDependency) return INetworkAnalysisDependency;
	hasRequiredINetworkAnalysisDependency = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.INetworkAnalysisDependencyModuleModeResolutionSchema = exports.INetworkAnalysisDependencyPortCharacteristicResolutionSchema = exports.NetworkAnalysisDependencyPortCharacteristicResolutionTypeSchema = exports.NetworkAnalysisDependencyPortCharacteristicResolutionType = exports.INetworkAnalysisDependencySchema = exports.INetworkAnalysisDependencyPortCharacteristicSchema = exports.INetworkAnalysisDependencyUnconstrainedModeSchema = exports.INetworkAnalysisDependencyModuleModeSchema = exports.NetworkAnalysisDependencyTypeSchema = exports.NetworkAnalysisDependencyType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
		const CharacteristicValue_1 = requireCharacteristicValue();
		var NetworkAnalysisDependencyType;
		(function (NetworkAnalysisDependencyType) {
		    NetworkAnalysisDependencyType["ModuleMode"] = "ModuleMode";
		    NetworkAnalysisDependencyType["PortCharacteristic"] = "PortCharacteristic";
		    NetworkAnalysisDependencyType["UnconstrainedMode"] = "UnconstrainedMode";
		})(NetworkAnalysisDependencyType || (exports.NetworkAnalysisDependencyType = NetworkAnalysisDependencyType = {}));
		exports.NetworkAnalysisDependencyTypeSchema = zod_1.default.nativeEnum(NetworkAnalysisDependencyType);
		exports.INetworkAnalysisDependencyModuleModeSchema = zod_1.default.object({
		    type: zod_1.default.literal(NetworkAnalysisDependencyType.ModuleMode),
		    modeGlobalId: zod_1.default.string(),
		    moduleName: zod_1.default.string(),
		});
		exports.INetworkAnalysisDependencyUnconstrainedModeSchema = zod_1.default.object({
		    type: zod_1.default.literal(NetworkAnalysisDependencyType.UnconstrainedMode),
		    modeGlobalId: zod_1.default.string(),
		    moduleName: zod_1.default.string(),
		});
		exports.INetworkAnalysisDependencyPortCharacteristicSchema = zod_1.default.object({
		    type: zod_1.default.literal(NetworkAnalysisDependencyType.PortCharacteristic),
		    portId: zod_1.default.string(),
		    netName: zod_1.default.string(),
		    characteristic: zod_1.default.lazy(() => BoundaryCharacteristic_1.BoundaryCharacteristicSchema),
		});
		exports.INetworkAnalysisDependencySchema = zod_1.default.discriminatedUnion('type', [
		    exports.INetworkAnalysisDependencyModuleModeSchema,
		    exports.INetworkAnalysisDependencyUnconstrainedModeSchema,
		    exports.INetworkAnalysisDependencyPortCharacteristicSchema,
		]);
		var NetworkAnalysisDependencyPortCharacteristicResolutionType;
		(function (NetworkAnalysisDependencyPortCharacteristicResolutionType) {
		    NetworkAnalysisDependencyPortCharacteristicResolutionType["BoundaryAnalysis"] = "BoundaryAnalysis";
		    NetworkAnalysisDependencyPortCharacteristicResolutionType["SpiceSimulation"] = "SpiceSimulation";
		    NetworkAnalysisDependencyPortCharacteristicResolutionType["Specified"] = "Specified";
		})(NetworkAnalysisDependencyPortCharacteristicResolutionType || (exports.NetworkAnalysisDependencyPortCharacteristicResolutionType = NetworkAnalysisDependencyPortCharacteristicResolutionType = {}));
		exports.NetworkAnalysisDependencyPortCharacteristicResolutionTypeSchema = zod_1.default.nativeEnum(NetworkAnalysisDependencyPortCharacteristicResolutionType);
		exports.INetworkAnalysisDependencyPortCharacteristicResolutionSchema = zod_1.default.object({
		    dependencyType: exports.NetworkAnalysisDependencyTypeSchema.refine((d) => d === NetworkAnalysisDependencyType.PortCharacteristic),
		    portId: zod_1.default.string(),
		    resolutionType: exports.NetworkAnalysisDependencyPortCharacteristicResolutionTypeSchema,
		    result: CharacteristicValue_1.CharacteristicValueSchema,
		});
		exports.INetworkAnalysisDependencyModuleModeResolutionSchema = zod_1.default.object({
		    dependencyType: exports.NetworkAnalysisDependencyTypeSchema.refine((d) => d === NetworkAnalysisDependencyType.ModuleMode),
		    modeGlobalId: zod_1.default.string(),
		    modeState: zod_1.default.boolean(),
		});
		
	} (INetworkAnalysisDependency));
	return INetworkAnalysisDependency;
}

var INetworkAnalysisPort = {};

var hasRequiredINetworkAnalysisPort;

function requireINetworkAnalysisPort () {
	if (hasRequiredINetworkAnalysisPort) return INetworkAnalysisPort;
	hasRequiredINetworkAnalysisPort = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.INetworkAnalysisPortSchema = exports.NetworkAnalysisPortTypeSchema = exports.NetworkAnalysisPortType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		var NetworkAnalysisPortType;
		(function (NetworkAnalysisPortType) {
		    NetworkAnalysisPortType["PowerNet"] = "PowerNet";
		    NetworkAnalysisPortType["GroundNet"] = "GroundNet";
		    NetworkAnalysisPortType["DigitalNet"] = "DigitalNet";
		    NetworkAnalysisPortType["AnyNet"] = "AnyNet";
		})(NetworkAnalysisPortType || (exports.NetworkAnalysisPortType = NetworkAnalysisPortType = {}));
		exports.NetworkAnalysisPortTypeSchema = zod_1.default.nativeEnum(NetworkAnalysisPortType);
		exports.INetworkAnalysisPortSchema = zod_1.default.object({
		    type: exports.NetworkAnalysisPortTypeSchema,
		    portId: zod_1.default.string(),
		    netName: zod_1.default.string(),
		});
		
	} (INetworkAnalysisPort));
	return INetworkAnalysisPort;
}

var ISpiceNetworkAnalysisResultSerialized = {};

var hasRequiredISpiceNetworkAnalysisResultSerialized;

function requireISpiceNetworkAnalysisResultSerialized () {
	if (hasRequiredISpiceNetworkAnalysisResultSerialized) return ISpiceNetworkAnalysisResultSerialized;
	hasRequiredISpiceNetworkAnalysisResultSerialized = 1;
	Object.defineProperty(ISpiceNetworkAnalysisResultSerialized, "__esModule", { value: true });
	ISpiceNetworkAnalysisResultSerialized.ISpiceNetworkAnalysisResultSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnalysisRequirements_1 = requireAnalysisRequirements();
	const NetStatsWithDependenciesSerialized_1 = requireNetStatsWithDependenciesSerialized();
	const ProjectModelNetStats_1 = requireProjectModelNetStats();
	const ISerializedCircuitState_1 = requireISerializedCircuitState();
	const ISimulationManifestEntry_1 = requireISimulationManifestEntry();
	ISpiceNetworkAnalysisResultSerialized.ISpiceNetworkAnalysisResultSerializedSchema = zod_1.default.object({
	    type: zod_1.default.literal(AnalysisRequirements_1.AnalysisType.SpiceNetworkAnalysis),
	    result: zod_1.default.object({
	        netStatsByModes: zod_1.default.lazy(() => NetStatsWithDependenciesSerialized_1.NetStatsWithDependenciesSerializedSchema).array(),
	        overallNetStats: ProjectModelNetStats_1.ProjectModelNetStatsSchema,
	        simulations: ISimulationManifestEntry_1.ISimulationManifestEntrySerializedSchema.array(),
	        simulationArtifacts: ISimulationManifestEntry_1.ISpiceSimulationArtifactSchema.array(),
	        circuitStates: ISerializedCircuitState_1.SerializedCircuitStateMapSchema,
	    }),
	});
	
	return ISpiceNetworkAnalysisResultSerialized;
}

var NetworkAnalysisNetwork = {};

var SerializedElectricalSchematicView = {};

var hasRequiredSerializedElectricalSchematicView;

function requireSerializedElectricalSchematicView () {
	if (hasRequiredSerializedElectricalSchematicView) return SerializedElectricalSchematicView;
	hasRequiredSerializedElectricalSchematicView = 1;
	Object.defineProperty(SerializedElectricalSchematicView, "__esModule", { value: true });
	SerializedElectricalSchematicView.SerializedElectricalSchematicViewSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	SerializedElectricalSchematicView.SerializedElectricalSchematicViewSchema = zod_1.default.object({
	    id: zod_1.default.string(),
	    nets: zod_1.default.record(zod_1.default.string(), zod_1.default.array(zod_1.default.object({
	        moduleRef: zod_1.default.string(),
	        moduleName: zod_1.default.string(),
	        pinRef: zod_1.default.string(),
	    }))),
	});
	
	return SerializedElectricalSchematicView;
}

var hasRequiredNetworkAnalysisNetwork;

function requireNetworkAnalysisNetwork () {
	if (hasRequiredNetworkAnalysisNetwork) return NetworkAnalysisNetwork;
	hasRequiredNetworkAnalysisNetwork = 1;
	Object.defineProperty(NetworkAnalysisNetwork, "__esModule", { value: true });
	NetworkAnalysisNetwork.INetworkAnalysisNetworkSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const INetworkAnalysisPort_1 = requireINetworkAnalysisPort();
	const INetworkAnalysisDependency_1 = requireINetworkAnalysisDependency();
	const SerializedElectricalSchematicView_1 = requireSerializedElectricalSchematicView();
	NetworkAnalysisNetwork.INetworkAnalysisNetworkSerializedSchema = zod_1.default.object({
	    network: SerializedElectricalSchematicView_1.SerializedElectricalSchematicViewSchema,
	    ports: INetworkAnalysisPort_1.INetworkAnalysisPortSchema.array(),
	    dependencies: INetworkAnalysisDependency_1.INetworkAnalysisDependencySchema.array(),
	});
	
	return NetworkAnalysisNetwork;
}

var NetworkAnalysisTypes = {};

var hasRequiredNetworkAnalysisTypes;

function requireNetworkAnalysisTypes () {
	if (hasRequiredNetworkAnalysisTypes) return NetworkAnalysisTypes;
	hasRequiredNetworkAnalysisTypes = 1;
	Object.defineProperty(NetworkAnalysisTypes, "__esModule", { value: true });
	NetworkAnalysisTypes.NetworkAnalysisModuleInclusionTypeSchema = NetworkAnalysisTypes.NetworkAnalysisModuleInclusionType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var NetworkAnalysisModuleInclusionType;
	(function (NetworkAnalysisModuleInclusionType) {
	    NetworkAnalysisModuleInclusionType["Full"] = "full";
	    NetworkAnalysisModuleInclusionType["Partial"] = "partial";
	})(NetworkAnalysisModuleInclusionType || (NetworkAnalysisTypes.NetworkAnalysisModuleInclusionType = NetworkAnalysisModuleInclusionType = {}));
	NetworkAnalysisTypes.NetworkAnalysisModuleInclusionTypeSchema = zod_1.default.nativeEnum(NetworkAnalysisModuleInclusionType);
	
	return NetworkAnalysisTypes;
}

var NetworkBuilderMessageResult = {};

var hasRequiredNetworkBuilderMessageResult;

function requireNetworkBuilderMessageResult () {
	if (hasRequiredNetworkBuilderMessageResult) return NetworkBuilderMessageResult;
	hasRequiredNetworkBuilderMessageResult = 1;
	Object.defineProperty(NetworkBuilderMessageResult, "__esModule", { value: true });
	NetworkBuilderMessageResult.NetworkBuilderMessageResultSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const MessageResult_1 = requireMessageResult();
	NetworkBuilderMessageResult.NetworkBuilderMessageResultSchema = (0, MessageResult_1.AggregateMessageResultSchema)(zod_1.default.literal('network-builder-result'), MessageResult_1.MessageResultSchemaDefault);
	
	return NetworkBuilderMessageResult;
}

var NetworkSegmentationResultSerialized = {};

var hasRequiredNetworkSegmentationResultSerialized;

function requireNetworkSegmentationResultSerialized () {
	if (hasRequiredNetworkSegmentationResultSerialized) return NetworkSegmentationResultSerialized;
	hasRequiredNetworkSegmentationResultSerialized = 1;
	Object.defineProperty(NetworkSegmentationResultSerialized, "__esModule", { value: true });
	NetworkSegmentationResultSerialized.NetworkSegmentationResultSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnalysisType_1 = requireAnalysisType$1();
	const NetworkBuilderMessageResult_1 = requireNetworkBuilderMessageResult();
	const NetworkAnalysisNetwork_1 = requireNetworkAnalysisNetwork();
	NetworkSegmentationResultSerialized.NetworkSegmentationResultSerializedSchema = zod_1.default.object({
	    type: zod_1.default.literal(AnalysisType_1.AnalysisType.NetworkAnalysisSegmentation),
	    result: zod_1.default.object({
	        processResult: NetworkBuilderMessageResult_1.NetworkBuilderMessageResultSchema,
	        viewNetworks: zod_1.default.record(zod_1.default.string(), NetworkAnalysisNetwork_1.INetworkAnalysisNetworkSerializedSchema),
	    }),
	});
	
	return NetworkSegmentationResultSerialized;
}

var PowerNetworkBuildResultSerialized = {};

var SerializedElectricalSchematicViewCollection = {};

var hasRequiredSerializedElectricalSchematicViewCollection;

function requireSerializedElectricalSchematicViewCollection () {
	if (hasRequiredSerializedElectricalSchematicViewCollection) return SerializedElectricalSchematicViewCollection;
	hasRequiredSerializedElectricalSchematicViewCollection = 1;
	Object.defineProperty(SerializedElectricalSchematicViewCollection, "__esModule", { value: true });
	SerializedElectricalSchematicViewCollection.SerializedElectricalSchematicViewCollectionSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SerializedElectricalSchematicView_1 = requireSerializedElectricalSchematicView();
	SerializedElectricalSchematicViewCollection.SerializedElectricalSchematicViewCollectionSchema = zod_1.default.object({
	    views: zod_1.default.array(SerializedElectricalSchematicView_1.SerializedElectricalSchematicViewSchema),
	});
	
	return SerializedElectricalSchematicViewCollection;
}

var hasRequiredPowerNetworkBuildResultSerialized;

function requirePowerNetworkBuildResultSerialized () {
	if (hasRequiredPowerNetworkBuildResultSerialized) return PowerNetworkBuildResultSerialized;
	hasRequiredPowerNetworkBuildResultSerialized = 1;
	Object.defineProperty(PowerNetworkBuildResultSerialized, "__esModule", { value: true });
	PowerNetworkBuildResultSerialized.PowerNetworkBuildResultSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnalysisType_1 = requireAnalysisType$1();
	const NetworkBuilderMessageResult_1 = requireNetworkBuilderMessageResult();
	const SerializedElectricalSchematicViewCollection_1 = requireSerializedElectricalSchematicViewCollection();
	PowerNetworkBuildResultSerialized.PowerNetworkBuildResultSerializedSchema = zod_1.default.object({
	    type: zod_1.default.literal(AnalysisType_1.AnalysisType.PowerNetworkBuild),
	    result: zod_1.default.object({
	        processResult: NetworkBuilderMessageResult_1.NetworkBuilderMessageResultSchema,
	        views: SerializedElectricalSchematicViewCollection_1.SerializedElectricalSchematicViewCollectionSchema,
	    }),
	});
	
	return PowerNetworkBuildResultSerialized;
}

var SegmentedNetworkAnalysisResultSerialized = {};

var hasRequiredSegmentedNetworkAnalysisResultSerialized;

function requireSegmentedNetworkAnalysisResultSerialized () {
	if (hasRequiredSegmentedNetworkAnalysisResultSerialized) return SegmentedNetworkAnalysisResultSerialized;
	hasRequiredSegmentedNetworkAnalysisResultSerialized = 1;
	Object.defineProperty(SegmentedNetworkAnalysisResultSerialized, "__esModule", { value: true });
	SegmentedNetworkAnalysisResultSerialized.SegmentedNetworkAnalysisOptsSerializedSchema = SegmentedNetworkAnalysisResultSerialized.SegmentedNetworkAnalysisResultSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnalysisRequirements_1 = requireAnalysisRequirements();
	const CircuitStates_1 = requireCircuitStates();
	const ProjectModelNetStats_1 = requireProjectModelNetStats();
	SegmentedNetworkAnalysisResultSerialized.SegmentedNetworkAnalysisResultSerializedSchema = zod_1.default.object({
	    type: zod_1.default.literal(AnalysisRequirements_1.AnalysisType.SegmentedNetworkAnalysis),
	    result: zod_1.default.object({
	        viewResults: zod_1.default.record(zod_1.default.string(), zod_1.default.string().array()),
	        droppedViews: zod_1.default.record(zod_1.default.string(), zod_1.default.string()),
	        aggregateNetStats: ProjectModelNetStats_1.ProjectModelNetStatsSchema,
	    }),
	});
	SegmentedNetworkAnalysisResultSerialized.SegmentedNetworkAnalysisOptsSerializedSchema = zod_1.default.object({
	    rootModeTreeVector: CircuitStates_1.IModeTreeStateVectorSerializedSchema,
	});
	
	return SegmentedNetworkAnalysisResultSerialized;
}

var SignalNetworkBuildResultSerialized = {};

var hasRequiredSignalNetworkBuildResultSerialized;

function requireSignalNetworkBuildResultSerialized () {
	if (hasRequiredSignalNetworkBuildResultSerialized) return SignalNetworkBuildResultSerialized;
	hasRequiredSignalNetworkBuildResultSerialized = 1;
	Object.defineProperty(SignalNetworkBuildResultSerialized, "__esModule", { value: true });
	SignalNetworkBuildResultSerialized.SignalNetworkBuildResultSerializedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AnalysisType_1 = requireAnalysisType$1();
	const NetworkBuilderMessageResult_1 = requireNetworkBuilderMessageResult();
	const SerializedElectricalSchematicViewCollection_1 = requireSerializedElectricalSchematicViewCollection();
	SignalNetworkBuildResultSerialized.SignalNetworkBuildResultSerializedSchema = zod_1.default.object({
	    type: zod_1.default.literal(AnalysisType_1.AnalysisType.SignalNetworkBuild),
	    result: zod_1.default.object({
	        processResult: NetworkBuilderMessageResult_1.NetworkBuilderMessageResultSchema,
	        views: SerializedElectricalSchematicViewCollection_1.SerializedElectricalSchematicViewCollectionSchema,
	        modulesIterated: zod_1.default.number(),
	    }),
	});
	
	return SignalNetworkBuildResultSerialized;
}

var hasRequiredNetworkAnalysis;

function requireNetworkAnalysis () {
	if (hasRequiredNetworkAnalysis) return NetworkAnalysis;
	hasRequiredNetworkAnalysis = 1;
	(function (exports) {
		var __createBinding = (NetworkAnalysis && NetworkAnalysis.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (NetworkAnalysis && NetworkAnalysis.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireINetworkAnalysisDependency(), exports);
		__exportStar(requireINetworkAnalysisPort(), exports);
		__exportStar(requireISpiceNetworkAnalysisResultSerialized(), exports);
		__exportStar(requireNetworkAnalysisNetwork(), exports);
		__exportStar(requireNetworkAnalysisTypes(), exports);
		__exportStar(requireNetworkBuilderMessageResult(), exports);
		__exportStar(requireNetworkSegmentationResultSerialized(), exports);
		__exportStar(requirePowerNetworkBuildResultSerialized(), exports);
		__exportStar(requireSegmentedNetworkAnalysisResultSerialized(), exports);
		__exportStar(requireSignalNetworkBuildResultSerialized(), exports);
		
	} (NetworkAnalysis));
	return NetworkAnalysis;
}

var hasRequiredElectricalSchematic;

function requireElectricalSchematic () {
	if (hasRequiredElectricalSchematic) return ElectricalSchematic;
	hasRequiredElectricalSchematic = 1;
	(function (exports) {
		var __createBinding = (ElectricalSchematic && ElectricalSchematic.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ElectricalSchematic && ElectricalSchematic.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireINetAnnotation(), exports);
		__exportStar(requireISerializedNetAnnotations(), exports);
		__exportStar(requireNetAnnotationResultSerialized(), exports);
		__exportStar(requireNetAnnotationType(), exports);
		__exportStar(requireNetBusClassAnnotationType(), exports);
		__exportStar(requireNetPowerAnnotationType(), exports);
		__exportStar(requireNetSignalAnnotationType(), exports);
		__exportStar(requireNetworkAnalysis(), exports);
		__exportStar(requireNetworkAnalysisTypes(), exports);
		__exportStar(requireSerializedElectricalSchematicView(), exports);
		__exportStar(requireSerializedElectricalSchematicViewCollection(), exports);
		
	} (ElectricalSchematic));
	return ElectricalSchematic;
}

var types$4 = {};

var Finding = {};

var RequirementInstanceResult = {};

var RequirementInstanceResultStatus = {};

var hasRequiredRequirementInstanceResultStatus;

function requireRequirementInstanceResultStatus () {
	if (hasRequiredRequirementInstanceResultStatus) return RequirementInstanceResultStatus;
	hasRequiredRequirementInstanceResultStatus = 1;
	Object.defineProperty(RequirementInstanceResultStatus, "__esModule", { value: true });
	RequirementInstanceResultStatus.RequirementInstanceResultStatusSchema = RequirementInstanceResultStatus.RequirementInstanceResultStatus = void 0;
	RequirementInstanceResultStatus.convertStatusToEnum = convertStatusToEnum;
	RequirementInstanceResultStatus.convertBoundaryLevelToRequirementStatus = convertBoundaryLevelToRequirementStatus;
	const zod_1 = /*@__PURE__*/ requireZod();
	const BoundaryLevel_1 = requireBoundaryLevel();
	var RequirementInstanceResultStatus$1;
	(function (RequirementInstanceResultStatus) {
	    RequirementInstanceResultStatus["Error"] = "error";
	    RequirementInstanceResultStatus["Warning"] = "warning";
	    RequirementInstanceResultStatus["Success"] = "success";
	    RequirementInstanceResultStatus["HumanNeeded"] = "humanNeeded";
	    RequirementInstanceResultStatus["Exception"] = "exception";
	})(RequirementInstanceResultStatus$1 || (RequirementInstanceResultStatus.RequirementInstanceResultStatus = RequirementInstanceResultStatus$1 = {}));
	RequirementInstanceResultStatus.RequirementInstanceResultStatusSchema = zod_1.z.nativeEnum(RequirementInstanceResultStatus$1);
	function convertStatusToEnum(status) {
	    switch (status) {
	        case 'error':
	            return RequirementInstanceResultStatus$1.Error;
	        case 'warning':
	            return RequirementInstanceResultStatus$1.Warning;
	        case 'success':
	            return RequirementInstanceResultStatus$1.Success;
	        case 'humanNeeded':
	            return RequirementInstanceResultStatus$1.HumanNeeded;
	        case 'exception':
	            return RequirementInstanceResultStatus$1.Exception;
	        default:
	            throw new Error(`Unknown status: ${status}`);
	    }
	}
	function convertBoundaryLevelToRequirementStatus(level) {
	    switch (level) {
	        case BoundaryLevel_1.BoundaryLevel.Error:
	            return RequirementInstanceResultStatus$1.Error;
	        case BoundaryLevel_1.BoundaryLevel.Warning:
	            return RequirementInstanceResultStatus$1.Warning;
	        case BoundaryLevel_1.BoundaryLevel.Recommendation:
	            return RequirementInstanceResultStatus$1.Success;
	        default:
	            return RequirementInstanceResultStatus$1.Exception;
	    }
	}
	
	return RequirementInstanceResultStatus;
}

var RequirementInstanceResultOverrideStatus = {};

var hasRequiredRequirementInstanceResultOverrideStatus;

function requireRequirementInstanceResultOverrideStatus () {
	if (hasRequiredRequirementInstanceResultOverrideStatus) return RequirementInstanceResultOverrideStatus;
	hasRequiredRequirementInstanceResultOverrideStatus = 1;
	Object.defineProperty(RequirementInstanceResultOverrideStatus, "__esModule", { value: true });
	RequirementInstanceResultOverrideStatus.RequirementInstanceResultOverrideStatusSchema = RequirementInstanceResultOverrideStatus.RequirementInstanceResultOverrideStatus = void 0;
	RequirementInstanceResultOverrideStatus.convertOverrideStatusToEnum = convertOverrideStatusToEnum;
	const zod_1 = /*@__PURE__*/ requireZod();
	var RequirementInstanceResultOverrideStatus$1;
	(function (RequirementInstanceResultOverrideStatus) {
	    RequirementInstanceResultOverrideStatus["Error"] = "error";
	    RequirementInstanceResultOverrideStatus["Warning"] = "warning";
	    RequirementInstanceResultOverrideStatus["Success"] = "success";
	})(RequirementInstanceResultOverrideStatus$1 || (RequirementInstanceResultOverrideStatus.RequirementInstanceResultOverrideStatus = RequirementInstanceResultOverrideStatus$1 = {}));
	RequirementInstanceResultOverrideStatus.RequirementInstanceResultOverrideStatusSchema = zod_1.z.nativeEnum(RequirementInstanceResultOverrideStatus$1);
	function convertOverrideStatusToEnum(status) {
	    switch (status) {
	        case 'error':
	            return RequirementInstanceResultOverrideStatus$1.Error;
	        case 'warning':
	            return RequirementInstanceResultOverrideStatus$1.Warning;
	        case 'success':
	            return RequirementInstanceResultOverrideStatus$1.Success;
	        default:
	            throw new Error(`Unknown override status: ${status}`);
	    }
	}
	
	return RequirementInstanceResultOverrideStatus;
}

var RequirementInstanceResults = {};

var PinoutValidationInstanceMetadata = {};

var hasRequiredPinoutValidationInstanceMetadata;

function requirePinoutValidationInstanceMetadata () {
	if (hasRequiredPinoutValidationInstanceMetadata) return PinoutValidationInstanceMetadata;
	hasRequiredPinoutValidationInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PinoutValidationResultMetadataSchema = exports.PinoutValidationResultEntrySchema = exports.PinoutValidationResultEntryStatusSchema = exports.PinoutValidationResultEntryStatus = exports.PinoutValidationInstanceMetadataSchema = exports.PinoutValidationResultKey = exports.PinoutRequirementTypeId = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.PinoutRequirementTypeId = 'Pinout';
		exports.PinoutValidationResultKey = 'PinoutValidationResult';
		exports.PinoutValidationInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.PinoutValidationResultKey),
		    referenceDesignator: zod_1.z.string(),
		    packageName: zod_1.z.string(),
		    packageStorePackageId: zod_1.z.string(),
		});
		var PinoutValidationResultEntryStatus;
		(function (PinoutValidationResultEntryStatus) {
		    PinoutValidationResultEntryStatus["PASS"] = "PASS";
		    PinoutValidationResultEntryStatus["FAIL"] = "FAIL";
		    PinoutValidationResultEntryStatus["UNMAPPED"] = "UNMAPPED";
		    PinoutValidationResultEntryStatus["TWO_PIN"] = "2PIN";
		    PinoutValidationResultEntryStatus["ERROR"] = "ERROR";
		    PinoutValidationResultEntryStatus["NOT_UNDERSTOOD"] = "NOT_UNDERSTOOD";
		})(PinoutValidationResultEntryStatus || (exports.PinoutValidationResultEntryStatus = PinoutValidationResultEntryStatus = {}));
		exports.PinoutValidationResultEntryStatusSchema = zod_1.z.nativeEnum(PinoutValidationResultEntryStatus);
		exports.PinoutValidationResultEntrySchema = zod_1.z.object({
		    designSignal: zod_1.z.string().optional(),
		    designPin: zod_1.z.string().optional(),
		    packageStoreSignal: zod_1.z.string().optional(),
		    packageStorePin: zod_1.z.string().optional(),
		    status: exports.PinoutValidationResultEntryStatusSchema,
		});
		exports.PinoutValidationResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.PinoutValidationResultKey),
		    results: zod_1.z.array(exports.PinoutValidationResultEntrySchema),
		});
		
	} (PinoutValidationInstanceMetadata));
	return PinoutValidationInstanceMetadata;
}

var NetDriveCompatibilityRequirementInstanceResultMetadataSchema = {};

var hasRequiredNetDriveCompatibilityRequirementInstanceResultMetadataSchema;

function requireNetDriveCompatibilityRequirementInstanceResultMetadataSchema () {
	if (hasRequiredNetDriveCompatibilityRequirementInstanceResultMetadataSchema) return NetDriveCompatibilityRequirementInstanceResultMetadataSchema;
	hasRequiredNetDriveCompatibilityRequirementInstanceResultMetadataSchema = 1;
	Object.defineProperty(NetDriveCompatibilityRequirementInstanceResultMetadataSchema, "__esModule", { value: true });
	NetDriveCompatibilityRequirementInstanceResultMetadataSchema.NetDriveCompatibilityRequirementInstanceResultMetadataSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	NetDriveCompatibilityRequirementInstanceResultMetadataSchema.NetDriveCompatibilityRequirementInstanceResultMetadataSchema = zod_1.z.object({
	    type: zod_1.z.literal('NetDriveCompatibilityResultMetadata'),
	});
	
	return NetDriveCompatibilityRequirementInstanceResultMetadataSchema;
}

var ComponentLimitsValidationInstanceMetadata = {};

var hasRequiredComponentLimitsValidationInstanceMetadata;

function requireComponentLimitsValidationInstanceMetadata () {
	if (hasRequiredComponentLimitsValidationInstanceMetadata) return ComponentLimitsValidationInstanceMetadata;
	hasRequiredComponentLimitsValidationInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentLimitsValidationResultMetadataSchema = exports.ComponentLimitsValidationResultEntrySchema = exports.ComponentLimitsValidationResultEntryStatusSchema = exports.ComponentLimitsValidationResultEntryStatus = exports.ComponentLimitsValidationInstanceMetadataSchema = exports.ComponentLimitsValidationResultKey = exports.ComponentLimitsValidationRequirementTypeId = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IBoundaryDefinition_1 = requireIBoundaryDefinition();
		const BoundaryErrorsDefinition_1 = requireBoundaryErrorsDefinition();
		const CharacteristicStateSummaryExternal_1 = requireCharacteristicStateSummaryExternal();
		exports.ComponentLimitsValidationRequirementTypeId = 'BNDS';
		exports.ComponentLimitsValidationResultKey = 'ComponentLimitValidationResult';
		exports.ComponentLimitsValidationInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.ComponentLimitsValidationResultKey),
		    referenceDesignator: zod_1.z.string(),
		    moduleName: zod_1.z.string().optional(),
		    boundaries: IBoundaryDefinition_1.IBoundaryDefinitionWithIdSchema.array(),
		});
		var ComponentLimitsValidationResultEntryStatus;
		(function (ComponentLimitsValidationResultEntryStatus) {
		    ComponentLimitsValidationResultEntryStatus["PASS"] = "PASS";
		    ComponentLimitsValidationResultEntryStatus["FAIL"] = "FAIL";
		    ComponentLimitsValidationResultEntryStatus["UNKNOWN_NET_STATES"] = "UNKNOWN_NET_STATES";
		    ComponentLimitsValidationResultEntryStatus["ERROR"] = "ERROR";
		})(ComponentLimitsValidationResultEntryStatus || (exports.ComponentLimitsValidationResultEntryStatus = ComponentLimitsValidationResultEntryStatus = {}));
		exports.ComponentLimitsValidationResultEntryStatusSchema = zod_1.z.nativeEnum(ComponentLimitsValidationResultEntryStatus);
		exports.ComponentLimitsValidationResultEntrySchema = zod_1.z.object({
		    errors: zod_1.z.lazy(() => BoundaryErrorsDefinition_1.IBoundaryErrorSchema).array(),
		    netValues: zod_1.z
		        .object({
		        net: zod_1.z.string(),
		        characteristics: CharacteristicStateSummaryExternal_1.CharacteristicStateSummaryExternalSchema.array(),
		    })
		        .array(),
		    status: exports.ComponentLimitsValidationResultEntryStatusSchema,
		});
		exports.ComponentLimitsValidationResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.ComponentLimitsValidationResultKey),
		    results: zod_1.z.lazy(() => exports.ComponentLimitsValidationResultEntrySchema).array(),
		});
		
	} (ComponentLimitsValidationInstanceMetadata));
	return ComponentLimitsValidationInstanceMetadata;
}

var ComponentValueValidationInstanceMetadata = {};

var hasRequiredComponentValueValidationInstanceMetadata;

function requireComponentValueValidationInstanceMetadata () {
	if (hasRequiredComponentValueValidationInstanceMetadata) return ComponentValueValidationInstanceMetadata;
	hasRequiredComponentValueValidationInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentValueValidationResultMetadataSchema = exports.ComponentValueValidationInstanceMetadataSchema = exports.ComponentValueValidationPackageDataSchema = exports.ComponentValueValidationDesignDataSchema = exports.ComponentValueValidationResultKey = exports.ComponentValueValidationRequirementTypeId = void 0;
		exports.getDrawPropertyValues = getDrawPropertyValues;
		const zod_1 = /*@__PURE__*/ requireZod();
		const metadata_1 = requireMetadata();
		const ModuleDrawProperty_1 = requireModuleDrawProperty();
		exports.ComponentValueValidationRequirementTypeId = 'CVAL';
		exports.ComponentValueValidationResultKey = 'ComponentValueValidationResult';
		exports.ComponentValueValidationDesignDataSchema = zod_1.z.object({
		    value: zod_1.z.string().optional(),
		    metadata: metadata_1.MetadataSchema.optional(),
		    drawProperties: zod_1.z.record(zod_1.z.string(), ModuleDrawProperty_1.ModuleDrawPropertySchema).optional(),
		});
		function getDrawPropertyValues(drawProperties) {
		    if (!drawProperties) {
		        return undefined;
		    }
		    const filteredProps = Object.entries(drawProperties).filter(([, prop]) => !prop.hidden);
		    return Object.fromEntries(filteredProps.map(([key, prop]) => [
		        key,
		        {
		            value: prop.value,
		        },
		    ]));
		}
		exports.ComponentValueValidationPackageDataSchema = zod_1.z.object({
		    resolutionType: zod_1.z.string().optional(),
		    value: zod_1.z.string().optional(),
		    manufacturer: zod_1.z.string().optional(),
		    partNumber: zod_1.z.string().optional(),
		});
		exports.ComponentValueValidationInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.ComponentValueValidationResultKey),
		    referenceDesignator: zod_1.z.string(),
		    designData: exports.ComponentValueValidationDesignDataSchema,
		    packageData: exports.ComponentValueValidationPackageDataSchema,
		});
		exports.ComponentValueValidationResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.ComponentValueValidationResultKey),
		    message: zod_1.z.string(),
		    packageRawValue: zod_1.z.string(),
		    projectRawValue: zod_1.z.string(),
		    packageValue: zod_1.z.string(),
		    projectValue: zod_1.z.string(),
		});
		
	} (ComponentValueValidationInstanceMetadata));
	return ComponentValueValidationInstanceMetadata;
}

var LogicalBusValidationInstanceMetadata = {};

var hasRequiredLogicalBusValidationInstanceMetadata;

function requireLogicalBusValidationInstanceMetadata () {
	if (hasRequiredLogicalBusValidationInstanceMetadata) return LogicalBusValidationInstanceMetadata;
	hasRequiredLogicalBusValidationInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.LogicalBusResultMetadataSchema = exports.LogicalBusValidationErrorSchema = exports.LogicalBusValidationErrorSeveritySchema = exports.LogicalBusValidationErrorSeverity = exports.LogicalBusValidationErrorTypeSchema = exports.LogicalBusValidationErrorType = exports.LogicalBusInstanceMetadataSchema = exports.LogicalBusValidationResultKey = exports.LogicalBusValidationRequirementTypeId = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.LogicalBusValidationRequirementTypeId = 'LogicalBus';
		exports.LogicalBusValidationResultKey = 'LogicalBusValidationResult';
		exports.LogicalBusInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.LogicalBusValidationResultKey),
		    busName: zod_1.z.string(),
		    functionClass: zod_1.z.string(),
		    components: zod_1.z.array(zod_1.z.object({
		        moduleRef: zod_1.z.string(),
		        moduleLocalFunctionName: zod_1.z.string(),
		        displayProperties: zod_1.z.array(zod_1.z.object({
		            type: zod_1.z.string(),
		            value: zod_1.z.string(),
		            moduleSignal: zod_1.z.string().nullish(),
		        })),
		    })),
		    connections: zod_1.z.array(zod_1.z.object({
		        functionSignal: zod_1.z.string(),
		        net: zod_1.z.string(),
		        moduleSignalId: zod_1.z.string(),
		        moduleRef: zod_1.z.string(),
		    })),
		});
		var LogicalBusValidationErrorType;
		(function (LogicalBusValidationErrorType) {
		    LogicalBusValidationErrorType["VALID_CONNECTIONS"] = "LogicalBus.ValidConnections";
		    LogicalBusValidationErrorType["NO_CONNECT"] = "LogicalBus.NoConnect";
		    LogicalBusValidationErrorType["UNSUPPORTED_FUNCTION"] = "LogicalBus.UnsupportedFunction";
		    LogicalBusValidationErrorType["VALID_ADDRESSES"] = "LogicalBus.ValidAddresses";
		    LogicalBusValidationErrorType["ADDRESS_CONTENTION"] = "LogicalBus.AddressContention";
		    LogicalBusValidationErrorType["UNADDRESSED"] = "LogicalBus.Unaddressed";
		    LogicalBusValidationErrorType["NET_NAME_CONFLICT"] = "Other.Warning.NetNameConflict";
		})(LogicalBusValidationErrorType || (exports.LogicalBusValidationErrorType = LogicalBusValidationErrorType = {}));
		exports.LogicalBusValidationErrorTypeSchema = zod_1.z.nativeEnum(LogicalBusValidationErrorType);
		var LogicalBusValidationErrorSeverity;
		(function (LogicalBusValidationErrorSeverity) {
		    LogicalBusValidationErrorSeverity["PASS"] = "Pass";
		    LogicalBusValidationErrorSeverity["FAIL"] = "Fail";
		    LogicalBusValidationErrorSeverity["WARNING"] = "Warning";
		})(LogicalBusValidationErrorSeverity || (exports.LogicalBusValidationErrorSeverity = LogicalBusValidationErrorSeverity = {}));
		exports.LogicalBusValidationErrorSeveritySchema = zod_1.z.nativeEnum(LogicalBusValidationErrorSeverity);
		exports.LogicalBusValidationErrorSchema = zod_1.z.object({
		    errorType: exports.LogicalBusValidationErrorTypeSchema,
		    severity: exports.LogicalBusValidationErrorSeveritySchema,
		    message: zod_1.z.string(),
		    moduleRef: zod_1.z.string().nullish(),
		});
		exports.LogicalBusResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.LogicalBusValidationResultKey),
		    busValidations: zod_1.z.array(exports.LogicalBusValidationErrorSchema),
		});
		
	} (LogicalBusValidationInstanceMetadata));
	return LogicalBusValidationInstanceMetadata;
}

var ComponentFunctionRequirementInstanceMetadata = {};

var hasRequiredComponentFunctionRequirementInstanceMetadata;

function requireComponentFunctionRequirementInstanceMetadata () {
	if (hasRequiredComponentFunctionRequirementInstanceMetadata) return ComponentFunctionRequirementInstanceMetadata;
	hasRequiredComponentFunctionRequirementInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentFunctionRequirementResultMetadataSchema = exports.ComponentFunctionRequirementInstanceMetadataSchema = exports.componentFunctionRequirementResultTypeName = exports.ComponentFunctionValidationResultKey = exports.ComponentFunctionValidationRequirementTypeId = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IInsightError_1 = requireIInsightError();
		exports.ComponentFunctionValidationRequirementTypeId = 'ComponentFunction';
		exports.ComponentFunctionValidationResultKey = 'ComponentFunctionValidationResult';
		exports.componentFunctionRequirementResultTypeName = 'ComponentFunctionResult';
		exports.ComponentFunctionRequirementInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.ComponentFunctionValidationResultKey),
		    moduleName: zod_1.z.string().optional(),
		    componentFunctionId: zod_1.z.string(),
		});
		exports.ComponentFunctionRequirementResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.componentFunctionRequirementResultTypeName),
		    insights: zod_1.z.lazy(() => IInsightError_1.IInsightErrorSchema).array(),
		});
		
	} (ComponentFunctionRequirementInstanceMetadata));
	return ComponentFunctionRequirementInstanceMetadata;
}

var NetDriveCompatibilityRequirementResultDetails = {};

var ModuleSignalDriveMode = {};

var ExpressionVariableValues = {};

var hasRequiredExpressionVariableValues;

function requireExpressionVariableValues () {
	if (hasRequiredExpressionVariableValues) return ExpressionVariableValues;
	hasRequiredExpressionVariableValues = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ExpressionDetailsSchema = exports.ExpressionVariableSchema = void 0;
		exports.convertVariablesRecordToArray = convertVariablesRecordToArray;
		const zod_1 = /*@__PURE__*/ requireZod();
		const AbsoluteCharacteristicValueBase_1 = requireAbsoluteCharacteristicValueBase();
		const BoundaryCharacteristic_1 = requireBoundaryCharacteristic();
		exports.ExpressionVariableSchema = zod_1.z.object({
		    variablename: zod_1.z.string(),
		    originalName: zod_1.z.string().optional(),
		    value: AbsoluteCharacteristicValueBase_1.AbsoluteCharacteristicValueBaseSchema,
		});
		exports.ExpressionDetailsSchema = zod_1.z.object({
		    expression: zod_1.z.string(),
		    evaluatedValue: AbsoluteCharacteristicValueBase_1.AbsoluteCharacteristicValueBaseSchema.nullable(),
		    variables: zod_1.z.array(exports.ExpressionVariableSchema).nullable(),
		    characteristic: BoundaryCharacteristic_1.BoundaryCharacteristicSchema,
		});
		function convertVariablesRecordToArray(variables, expressionVariables) {
		    if (!variables) {
		        return null;
		    }
		    return Object.entries(variables).map(([variablename, value]) => {
		        const expressionVar = expressionVariables?.find((v) => v.id === variablename);
		        return {
		            variablename,
		            originalName: expressionVar?.originalName,
		            value,
		        };
		    });
		}
		
	} (ExpressionVariableValues));
	return ExpressionVariableValues;
}

var hasRequiredModuleSignalDriveMode;

function requireModuleSignalDriveMode () {
	if (hasRequiredModuleSignalDriveMode) return ModuleSignalDriveMode;
	hasRequiredModuleSignalDriveMode = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModuleSignalDriveModeSchema = exports.__ModuleDriveModeSchemaInternal = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const ExpressionVariableValues_1 = requireExpressionVariableValues();
		exports.__ModuleDriveModeSchemaInternal = zod_1.z.object({
		    moduleId: zod_1.z.string(),
		    signalId: zod_1.z.string().nullable(),
		    driveType: zod_1.z.string().nullable(),
		    driveModeConditionNames: zod_1.z.array(zod_1.z.string()).nullable().optional(),
		    outputHighVoltageDetails: ExpressionVariableValues_1.ExpressionDetailsSchema.nullable(),
		    outputLowVoltageDetails: ExpressionVariableValues_1.ExpressionDetailsSchema.nullable(),
		    inputHighVoltageDetails: ExpressionVariableValues_1.ExpressionDetailsSchema.nullable(),
		    inputLowVoltageDetails: ExpressionVariableValues_1.ExpressionDetailsSchema.nullable(),
		    modeActiveState: zod_1.z.string().nullable(),
		    traversalNetName: zod_1.z.string().optional(),
		    traversalPaths: zod_1.z.array(zod_1.z.string()).optional(),
		});
		exports.ModuleSignalDriveModeSchema = zod_1.z.preprocess((data) => {
		    if (typeof data === 'object' && data !== null && 'driveModeContidionNames' in data) {
		        const record = data;
		        record.driveModeConditionNames = record.driveModeContidionNames;
		        delete record.driveModeContidionNames;
		    }
		    return data;
		}, exports.__ModuleDriveModeSchemaInternal);
		
	} (ModuleSignalDriveMode));
	return ModuleSignalDriveMode;
}

var hasRequiredNetDriveCompatibilityRequirementResultDetails;

function requireNetDriveCompatibilityRequirementResultDetails () {
	if (hasRequiredNetDriveCompatibilityRequirementResultDetails) return NetDriveCompatibilityRequirementResultDetails;
	hasRequiredNetDriveCompatibilityRequirementResultDetails = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetDriveCompatibilityRequirementResultDetailsSchema = exports.NetDriveCompatibilityErrorSchema = void 0;
		const ModuleSignalDriveMode_1 = requireModuleSignalDriveMode();
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.NetDriveCompatibilityErrorSchema = zod_1.z.object({
		    type: zod_1.z.string(),
		    message: zod_1.z.string().optional(),
		});
		exports.NetDriveCompatibilityRequirementResultDetailsSchema = zod_1.z.object({
		    type: zod_1.z.literal('NetDriveCompatibilityResult'),
		    net: zod_1.z.string(),
		    modules: zod_1.z.array(ModuleSignalDriveMode_1.ModuleSignalDriveModeSchema),
		    biasVoltage: zod_1.z.string().optional(),
		    errors: zod_1.z.array(exports.NetDriveCompatibilityErrorSchema).optional(),
		});
		
	} (NetDriveCompatibilityRequirementResultDetails));
	return NetDriveCompatibilityRequirementResultDetails;
}

var ComponentResourceRequirementInstanceMetadata = {};

var hasRequiredComponentResourceRequirementInstanceMetadata;

function requireComponentResourceRequirementInstanceMetadata () {
	if (hasRequiredComponentResourceRequirementInstanceMetadata) return ComponentResourceRequirementInstanceMetadata;
	hasRequiredComponentResourceRequirementInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ComponentResourceRequirementResultMetadataSchema = exports.ComponentResourceRequirementInstanceMetadataSchema = exports.componentResourceRequirementResultTypeName = exports.ComponentResourceValidationResultKey = exports.ComponentResourceValidationRequirementTypeId = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IInsightError_1 = requireIInsightError();
		exports.ComponentResourceValidationRequirementTypeId = 'ComponentResource';
		exports.ComponentResourceValidationResultKey = 'ComponentResourceValidationResult';
		exports.componentResourceRequirementResultTypeName = 'ComponentResourceResult';
		exports.ComponentResourceRequirementInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.ComponentResourceValidationResultKey),
		    moduleName: zod_1.z.string().optional(),
		    componentResourceId: zod_1.z.string(),
		});
		exports.ComponentResourceRequirementResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.componentResourceRequirementResultTypeName),
		    insights: zod_1.z.lazy(() => IInsightError_1.IInsightErrorSchema).array(),
		});
		
	} (ComponentResourceRequirementInstanceMetadata));
	return ComponentResourceRequirementInstanceMetadata;
}

var hasRequiredRequirementInstanceResults;

function requireRequirementInstanceResults () {
	if (hasRequiredRequirementInstanceResults) return RequirementInstanceResults;
	hasRequiredRequirementInstanceResults = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RequirementInstanceResultMetadataSchema = exports.RequirementInstanceMetadataSchema = exports.InstanceResultMetadataSchemas = exports.InstanceMetadataSchemas = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const PinoutValidationInstanceMetadata_1 = requirePinoutValidationInstanceMetadata();
		const NetDriveCompatibilityRequirementInstanceResultMetadataSchema_1 = requireNetDriveCompatibilityRequirementInstanceResultMetadataSchema();
		const ComponentLimitsValidationInstanceMetadata_1 = requireComponentLimitsValidationInstanceMetadata();
		const ComponentValueValidationInstanceMetadata_1 = requireComponentValueValidationInstanceMetadata();
		const LogicalBusValidationInstanceMetadata_1 = requireLogicalBusValidationInstanceMetadata();
		const ComponentFunctionRequirementInstanceMetadata_1 = requireComponentFunctionRequirementInstanceMetadata();
		const NetDriveCompatibilityRequirementResultDetails_1 = requireNetDriveCompatibilityRequirementResultDetails();
		const ComponentResourceRequirementInstanceMetadata_1 = requireComponentResourceRequirementInstanceMetadata();
		exports.InstanceMetadataSchemas = [
		    NetDriveCompatibilityRequirementResultDetails_1.NetDriveCompatibilityRequirementResultDetailsSchema,
		    PinoutValidationInstanceMetadata_1.PinoutValidationInstanceMetadataSchema,
		    ComponentLimitsValidationInstanceMetadata_1.ComponentLimitsValidationInstanceMetadataSchema,
		    ComponentValueValidationInstanceMetadata_1.ComponentValueValidationInstanceMetadataSchema,
		    LogicalBusValidationInstanceMetadata_1.LogicalBusInstanceMetadataSchema,
		    ComponentFunctionRequirementInstanceMetadata_1.ComponentFunctionRequirementInstanceMetadataSchema,
		    ComponentResourceRequirementInstanceMetadata_1.ComponentResourceRequirementInstanceMetadataSchema,
		];
		exports.InstanceResultMetadataSchemas = [
		    NetDriveCompatibilityRequirementInstanceResultMetadataSchema_1.NetDriveCompatibilityRequirementInstanceResultMetadataSchema,
		    PinoutValidationInstanceMetadata_1.PinoutValidationResultMetadataSchema,
		    ComponentLimitsValidationInstanceMetadata_1.ComponentLimitsValidationResultMetadataSchema,
		    ComponentValueValidationInstanceMetadata_1.ComponentValueValidationResultMetadataSchema,
		    LogicalBusValidationInstanceMetadata_1.LogicalBusResultMetadataSchema,
		    ComponentFunctionRequirementInstanceMetadata_1.ComponentFunctionRequirementResultMetadataSchema,
		    ComponentResourceRequirementInstanceMetadata_1.ComponentResourceRequirementResultMetadataSchema,
		];
		exports.RequirementInstanceMetadataSchema = zod_1.z.discriminatedUnion('type', exports.InstanceMetadataSchemas);
		exports.RequirementInstanceResultMetadataSchema = zod_1.z.discriminatedUnion('type', exports.InstanceResultMetadataSchemas);
		
	} (RequirementInstanceResults));
	return RequirementInstanceResults;
}

var RequirementInstanceNote = {};

var hasRequiredRequirementInstanceNote;

function requireRequirementInstanceNote () {
	if (hasRequiredRequirementInstanceNote) return RequirementInstanceNote;
	hasRequiredRequirementInstanceNote = 1;
	Object.defineProperty(RequirementInstanceNote, "__esModule", { value: true });
	RequirementInstanceNote.RequirementInstanceNoteSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const RequirementInstanceResultOverrideStatus_1 = requireRequirementInstanceResultOverrideStatus();
	RequirementInstanceNote.RequirementInstanceNoteSchema = zod_1.z.object({
	    id: zod_1.z.string(),
	    user: zod_1.z.string().optional(),
	    contents: zod_1.z.string().optional(),
	    analysisResultId: zod_1.z.string(),
	    createdAt: zod_1.z.coerce.date().optional(),
	    updatedAt: zod_1.z.coerce.date().optional(),
	    statusOverride: RequirementInstanceResultOverrideStatus_1.RequirementInstanceResultOverrideStatusSchema.optional(),
	});
	
	return RequirementInstanceNote;
}

var hasRequiredRequirementInstanceResult;

function requireRequirementInstanceResult () {
	if (hasRequiredRequirementInstanceResult) return RequirementInstanceResult;
	hasRequiredRequirementInstanceResult = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RequirementInstanceResultSchema = exports.RequirementResultTypeDisplayNames = exports.RequirementResultTypeSchema = exports.AnalysisResultTypeSchema = exports.RequirementResultType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const DesignObject_1 = requireDesignObject();
		const RequirementInstanceResultStatus_1 = requireRequirementInstanceResultStatus();
		const RequirementInstanceResultOverrideStatus_1 = requireRequirementInstanceResultOverrideStatus();
		const RequirementInstanceResults_1 = requireRequirementInstanceResults();
		const RequirementInstanceNote_1 = requireRequirementInstanceNote();
		var RequirementResultType;
		(function (RequirementResultType) {
		    RequirementResultType["NetDrive"] = "LOGIC";
		    RequirementResultType["Pinout"] = "PINS";
		    RequirementResultType["BNDS"] = "BNDS";
		    RequirementResultType["CVAL"] = "CVAL";
		    RequirementResultType["LogicalBus"] = "BUS";
		    RequirementResultType["ComponentFunction"] = "CFUN";
		    RequirementResultType["ComponentResource"] = "CRES";
		})(RequirementResultType || (exports.RequirementResultType = RequirementResultType = {}));
		exports.AnalysisResultTypeSchema = zod_1.z.nativeEnum(RequirementResultType);
		exports.RequirementResultTypeSchema = zod_1.z.nativeEnum(RequirementResultType);
		exports.RequirementResultTypeDisplayNames = {
		    [RequirementResultType.NetDrive]: 'Logic Threshold Validation',
		    [RequirementResultType.Pinout]: 'Pinout Validation',
		    [RequirementResultType.BNDS]: 'Component Limits Validation',
		    [RequirementResultType.CVAL]: 'Component Value Validation',
		    [RequirementResultType.LogicalBus]: 'Bus Validation',
		    [RequirementResultType.ComponentFunction]: 'Component Function Validation',
		    [RequirementResultType.ComponentResource]: 'Component Resource Validation',
		};
		exports.RequirementInstanceResultSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    requirementId: zod_1.z.string(),
		    typeId: zod_1.z.string().transform((v) => {
		        if (v === 'NetDrive') {
		            return RequirementResultType.NetDrive;
		        }
		        if (v === 'Pinout') {
		            return RequirementResultType.Pinout;
		        }
		        if (v === 'LogicalBus') {
		            return RequirementResultType.LogicalBus;
		        }
		        if (v === 'ComponentFunction') {
		            return RequirementResultType.ComponentFunction;
		        }
		        if (v === 'ComponentResource') {
		            return RequirementResultType.ComponentResource;
		        }
		        return exports.AnalysisResultTypeSchema.parse(v);
		    }),
		    projectId: zod_1.z.string(),
		    buildId: zod_1.z.string().optional(),
		    expected: zod_1.z.string(),
		    actual: zod_1.z.string(),
		    status: zod_1.z.lazy(() => RequirementInstanceResultStatus_1.RequirementInstanceResultStatusSchema),
		    statusOverride: zod_1.z
		        .lazy(() => RequirementInstanceResultOverrideStatus_1.RequirementInstanceResultOverrideStatusSchema)
		        .nullable()
		        .optional(),
		    isReviewed: zod_1.z.boolean().optional(),
		    notes: zod_1.z
		        .lazy(() => RequirementInstanceNote_1.RequirementInstanceNoteSchema)
		        .array()
		        .optional(),
		    notesCount: zod_1.z.number().optional(),
		    instanceMetadata: zod_1.z.lazy(() => RequirementInstanceResults_1.RequirementInstanceMetadataSchema).optional(),
		    resultMetadata: zod_1.z.lazy(() => RequirementInstanceResults_1.RequirementInstanceResultMetadataSchema).optional(),
		    designObjects: zod_1.z.lazy(() => DesignObject_1.DesignObjectSchema).array(),
		});
		
	} (RequirementInstanceResult));
	return RequirementInstanceResult;
}

var hasRequiredFinding;

function requireFinding () {
	if (hasRequiredFinding) return Finding;
	hasRequiredFinding = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FindingSchema = exports.FreeformFindingSchema = exports.NetDriveFindingSchema = exports.BaseFindingSchema = exports.FindingDesignObjectTagSchema = exports.FindingStatusSchema = exports.FindingStatus = exports.FindingTypeSchema = exports.FindingType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const Claim_1 = requireClaim();
		const RequirementInstanceResult_1 = requireRequirementInstanceResult();
		const DesignObject_1 = requireDesignObject();
		var FindingType;
		(function (FindingType) {
		    FindingType["Freeform"] = "FreeformFinding";
		    FindingType["NetDrive"] = "NetDriveFinding";
		})(FindingType || (exports.FindingType = FindingType = {}));
		exports.FindingTypeSchema = zod_1.z.nativeEnum(FindingType);
		var FindingStatus;
		(function (FindingStatus) {
		    FindingStatus["Success"] = "success";
		    FindingStatus["Recommendation"] = "recommendation";
		    FindingStatus["Warning"] = "warning";
		    FindingStatus["Error"] = "error";
		})(FindingStatus || (exports.FindingStatus = FindingStatus = {}));
		exports.FindingStatusSchema = zod_1.z.nativeEnum(FindingStatus);
		exports.FindingDesignObjectTagSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    type: DesignObject_1.DesignObjectTypeSchema,
		    reference: zod_1.z.string(),
		});
		exports.BaseFindingSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    type: exports.FindingTypeSchema,
		    projectId: zod_1.z.string(),
		    buildId: zod_1.z.string().optional(),
		    status: exports.FindingStatusSchema,
		    isReviewed: zod_1.z.boolean(),
		    title: zod_1.z.string(),
		    description: zod_1.z.string(),
		    designObjectTags: exports.FindingDesignObjectTagSchema.array(),
		    analysisResults: RequirementInstanceResult_1.RequirementInstanceResultSchema.array().optional(),
		});
		exports.NetDriveFindingSchema = exports.BaseFindingSchema.extend({
		    type: zod_1.z.literal(FindingType.NetDrive),
		    requirementResultIds: zod_1.z.string().array().optional(),
		    sources: Claim_1.ClaimSchema.array(),
		    sinks: Claim_1.ClaimSchema.array(),
		    connections: Claim_1.ClaimSchema.array(),
		});
		exports.FreeformFindingSchema = exports.BaseFindingSchema.extend({
		    type: zod_1.z.literal(FindingType.Freeform),
		    claims: Claim_1.ClaimSchema.array(),
		});
		exports.FindingSchema = zod_1.z.discriminatedUnion('type', [
		    exports.NetDriveFindingSchema,
		    exports.FreeformFindingSchema,
		]);
		
	} (Finding));
	return Finding;
}

var hasRequiredTypes$4;

function requireTypes$4 () {
	if (hasRequiredTypes$4) return types$4;
	hasRequiredTypes$4 = 1;
	(function (exports) {
		var __createBinding = (types$4 && types$4.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (types$4 && types$4.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireFinding(), exports);
		
	} (types$4));
	return types$4;
}

var IElectricalSchematicTraversalHistory = {};

var hasRequiredIElectricalSchematicTraversalHistory;

function requireIElectricalSchematicTraversalHistory () {
	if (hasRequiredIElectricalSchematicTraversalHistory) return IElectricalSchematicTraversalHistory;
	hasRequiredIElectricalSchematicTraversalHistory = 1;
	Object.defineProperty(IElectricalSchematicTraversalHistory, "__esModule", { value: true });
	
	return IElectricalSchematicTraversalHistory;
}

var InferenceReportUtil = {};

var hasRequiredInferenceReportUtil;

function requireInferenceReportUtil () {
	if (hasRequiredInferenceReportUtil) return InferenceReportUtil;
	hasRequiredInferenceReportUtil = 1;
	Object.defineProperty(InferenceReportUtil, "__esModule", { value: true });
	InferenceReportUtil.InferenceReportUtil = void 0;
	class InferenceReportUtil$1 {
	    static getParameterValue(report, parameterName) {
	        return report.parameters?.[parameterName];
	    }
	    static getParameterValueVarianceMap(report) {
	        return Object.fromEntries(Object.entries(report.parameters ?? {}).map(([key, value]) => [
	            key,
	            typeof value === 'object' ? value?.value : { value: value },
	        ]));
	    }
	    static setParameterValue(report, parameterName, value) {
	        if (!report.parameters) {
	            report.parameters = {};
	        }
	        report.parameters[parameterName] = value;
	    }
	    static getHighestPrioritySignalEntry(report, signalId) {
	        return report.signals?.find((e) => e.signalId === signalId);
	    }
	    static getEntriesForSignal(report, signalId) {
	        return report.signals?.filter((e) => e.signalId === signalId) ?? [];
	    }
	    static setSignalEntry(report, entry, replaceExistingFromSameSource = true) {
	        if (!report.signals) {
	            report.signals = [];
	        }
	        else if (replaceExistingFromSameSource) {
	            report.signals = report.signals.filter((e) => e.signalId !== entry.signalId || e.source !== entry.source);
	        }
	        report.signals.push(entry);
	        return report;
	    }
	}
	InferenceReportUtil.InferenceReportUtil = InferenceReportUtil$1;
	
	return InferenceReportUtil;
}

var ValueAndExpressionParsing = {};

var varianceChecker = {};

var hasRequiredVarianceChecker;

function requireVarianceChecker () {
	if (hasRequiredVarianceChecker) return varianceChecker;
	hasRequiredVarianceChecker = 1;
	Object.defineProperty(varianceChecker, "__esModule", { value: true });
	varianceChecker.VarianceChecker = void 0;
	const CommonParameterUnits_1 = requireCommonParameterUnits();
	const SIValueTool_1 = requireSIValueTool();
	class VarianceChecker {
	    static { this.plusMinusSign = '+/-'; }
	    static { this.ValueVarianceEpsilon = { variance: this.plusMinusSign + '0.00001%' }; }
	    static { this.VarianceZeroValueEpsilon = 1e-7; }
	    static compareAbsoluteNumberWithVariance(value, expected, variance) {
	        const upperResult = VarianceChecker.compareMaxNumberWithVariance(value, expected, variance, true);
	        const lowerResult = VarianceChecker.compareMinNumberWithVariance(value, expected, variance, true);
	        return upperResult && lowerResult;
	    }
	    static compareMaxNumberWithVariance(value, expected, variance, valInclusive) {
	        const comparison = valInclusive ?
	            (value, assert) => value <= assert
	            : (value, assert) => value < assert;
	        const assertValue = VarianceChecker.getMaxValueWithVariance(expected, variance);
	        return comparison(value, assertValue);
	    }
	    static compareMinNumberWithVariance(value, expected, variance, valInclusive) {
	        const comparison = valInclusive ?
	            (value, assert) => value >= assert
	            : (value, assert) => value > assert;
	        const assertValue = VarianceChecker.getMinValueWithVariance(expected, variance);
	        return comparison(value, assertValue);
	    }
	    static getMaxValueWithVariance(value, variance, units) {
	        let varianceProps;
	        if (typeof variance === 'string') {
	            if (!units) {
	                throw new Error('Units must be provided when using string variance');
	            }
	            varianceProps = VarianceChecker.valueVarianceToProperties({ variance }, units);
	        }
	        else {
	            varianceProps = variance;
	        }
	        const varianceOperand = VarianceChecker.getVarianceOperand(value, varianceProps);
	        if (varianceProps?.singleSided) {
	            return varianceOperand > 0 ? value + varianceOperand : value;
	        }
	        const max = value + varianceOperand;
	        return max;
	    }
	    static getMinValueWithVariance(value, variance, units) {
	        let varianceProps;
	        if (typeof variance === 'string') {
	            if (!units) {
	                throw new Error('Units must be provided when using string variance');
	            }
	            varianceProps = VarianceChecker.valueVarianceToProperties({ variance }, units);
	        }
	        else {
	            varianceProps = variance;
	        }
	        const varianceOperand = VarianceChecker.getVarianceOperand(value, varianceProps);
	        if (varianceProps?.singleSided) {
	            return varianceOperand < 0 ? value + varianceOperand : value;
	        }
	        const min = value - varianceOperand;
	        return min;
	    }
	    static buildValueVariance(value, units, singleSided = false) {
	        let varianceString = '';
	        if (!singleSided) {
	            value = Math.abs(value);
	            varianceString += VarianceChecker.plusMinusSign;
	        }
	        else if (value > 0) {
	            varianceString += '+';
	        }
	        const valueStr = this.formatEngNotation(value);
	        varianceString += `${valueStr}${units.short}`;
	        return { variance: varianceString };
	    }
	    static valueVarianceToProperties(variance, baseUnits) {
	        const sanitizedValue = variance.variance?.replace(/\s/g, '') ?? '';
	        if (!sanitizedValue) {
	            return undefined;
	        }
	        const varianceRegex = /^((±|\+\/-|-\/\+)|[+-])?([0-9]*\.?[0-9]+(?:e[-+]?[0-9]+)?)(\D+)$/i;
	        const match = varianceRegex.exec(sanitizedValue);
	        if (!match) {
	            return undefined;
	        }
	        const isSingleSided = match[1] !== undefined && match[2] === undefined;
	        const valueStr = isSingleSided ? match[1] + match[3] : match[3];
	        const value = parseFloat(valueStr);
	        const valueAndUnits = value.toString() + match[4];
	        const vProps = VarianceChecker.parseSiVariance(valueAndUnits, baseUnits);
	        vProps.singleSided = isSingleSided;
	        return vProps;
	    }
	    static sanitizeValueVariance(variance, units) {
	        const varianceProps = VarianceChecker.valueVarianceToProperties(variance, units);
	        variance.variance = VarianceChecker.buildValueVariance(varianceProps.value, varianceProps.units, varianceProps.singleSided).variance;
	        return variance;
	    }
	    static parseSiVariance(value, units) {
	        let newValue;
	        let vProps;
	        try {
	            newValue = (0, SIValueTool_1.parseNumericalSIValue)(value, CommonParameterUnits_1.CommonParameterUnits.percentage);
	            vProps = {
	                value: newValue,
	                units: CommonParameterUnits_1.CommonParameterUnits.percentage,
	                singleSided: undefined,
	            };
	        }
	        catch {
	            newValue = (0, SIValueTool_1.parseNumericalSIValue)(value, units);
	            vProps = {
	                value: newValue,
	                units: units,
	                singleSided: undefined,
	            };
	        }
	        return vProps;
	    }
	    static formatEngNotation(num) {
	        let exp = Math.floor(Math.log10(Math.abs(num)));
	        exp = Math.round(exp / 3) * 3;
	        let base = num / Math.pow(10, exp);
	        if (Math.abs(base) < 1) {
	            base = base * Math.pow(10, 3);
	            exp -= 3;
	        }
	        let returnStr = base.toFixed(3);
	        returnStr = returnStr.replace(/\.?0*$/, '');
	        if (exp != 0) {
	            returnStr += 'e' + exp;
	        }
	        return returnStr;
	    }
	    static getVarianceOperand(value, variance) {
	        if (variance === undefined) {
	            return 0;
	        }
	        const isPercent = variance.units ? variance.units.short === CommonParameterUnits_1.CommonParameterUnits.percentage.short : false;
	        if (isPercent && Math.abs(value) < 10 * Number.EPSILON) {
	            return variance.value < 0 ? -VarianceChecker.VarianceZeroValueEpsilon : VarianceChecker.VarianceZeroValueEpsilon;
	        }
	        const operand = isPercent ? (Math.abs(value) * variance.value) / 100 : variance.value;
	        return operand;
	    }
	    static firstDefinedValue(...values) {
	        for (const value of values) {
	            if (value === undefined && value === null) {
	                continue;
	            }
	            if (typeof value === 'object') {
	                if (value?.value !== undefined && value?.value !== null) {
	                    return value.value;
	                }
	                continue;
	            }
	            return value;
	        }
	        return undefined;
	    }
	}
	varianceChecker.VarianceChecker = VarianceChecker;
	
	return varianceChecker;
}

var hasRequiredValueAndExpressionParsing;

function requireValueAndExpressionParsing () {
	if (hasRequiredValueAndExpressionParsing) return ValueAndExpressionParsing;
	hasRequiredValueAndExpressionParsing = 1;
	(function (exports) {
		var __createBinding = (ValueAndExpressionParsing && ValueAndExpressionParsing.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ValueAndExpressionParsing && ValueAndExpressionParsing.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireCharacteristicExpressionParser(), exports);
		__exportStar(requireSIValueTool(), exports);
		__exportStar(requireVarianceChecker(), exports);
		
	} (ValueAndExpressionParsing));
	return ValueAndExpressionParsing;
}

var ValidationChecks = {};

var tableValidationTools = {};

var validationCheckResult = {};

var hasRequiredValidationCheckResult;

function requireValidationCheckResult () {
	if (hasRequiredValidationCheckResult) return validationCheckResult;
	hasRequiredValidationCheckResult = 1;
	Object.defineProperty(validationCheckResult, "__esModule", { value: true });
	validationCheckResult.ValidationCheckResultStatus = void 0;
	validationCheckResult.createSkippedValidationEntries = createSkippedValidationEntries;
	validationCheckResult.filterToFailedValidationResults = filterToFailedValidationResults;
	validationCheckResult.compactValidationResults = compactValidationResults;
	var ValidationCheckResultStatus;
	(function (ValidationCheckResultStatus) {
	    ValidationCheckResultStatus["pass"] = "pass";
	    ValidationCheckResultStatus["fail"] = "fail";
	    ValidationCheckResultStatus["skipped"] = "skipped";
	})(ValidationCheckResultStatus || (validationCheckResult.ValidationCheckResultStatus = ValidationCheckResultStatus = {}));
	function createSkippedValidationEntries(results, errorTypeEnum) {
	    const allValidationTypes = Object.values(errorTypeEnum);
	    const typesInResults = new Set(results.map((result) => result.type));
	    const missingTypes = allValidationTypes.filter((type) => !typesInResults.has(type));
	    const skippedEntries = [];
	    for (const missingType of missingTypes) {
	        skippedEntries.push({
	            type: missingType,
	            result: ValidationCheckResultStatus.skipped,
	            message: 'Check was not performed',
	        });
	    }
	    return skippedEntries;
	}
	function filterToFailedValidationResults(results) {
	    return results.filter((entry) => entry.result === ValidationCheckResultStatus.fail);
	}
	function compactValidationResults(results) {
	    if (results.length === 0) {
	        return [];
	    }
	    const resultsByType = new Map();
	    for (const result of results) {
	        const existing = resultsByType.get(result.type);
	        if (existing) {
	            existing.push(result);
	        }
	        else {
	            resultsByType.set(result.type, [result]);
	        }
	    }
	    const compactedResults = [];
	    for (const [_type, typeResults] of resultsByType) {
	        const failures = typeResults.filter((r) => r.result === ValidationCheckResultStatus.fail);
	        const passes = typeResults.filter((r) => r.result === ValidationCheckResultStatus.pass);
	        const skipped = typeResults.filter((r) => r.result === ValidationCheckResultStatus.skipped);
	        if (failures.length > 0) {
	            compactedResults.push(...failures);
	        }
	        else if (passes.length > 0) {
	            compactedResults.push(passes[0]);
	        }
	        else if (skipped.length > 0) {
	            compactedResults.push(skipped[0]);
	        }
	    }
	    const statusPriority = {
	        [ValidationCheckResultStatus.pass]: 1,
	        [ValidationCheckResultStatus.fail]: 2,
	        [ValidationCheckResultStatus.skipped]: 3,
	    };
	    compactedResults.sort((a, b) => {
	        const statusDiff = statusPriority[a.result] - statusPriority[b.result];
	        if (statusDiff !== 0) {
	            return statusDiff;
	        }
	        return a.type.localeCompare(b.type);
	    });
	    return compactedResults;
	}
	
	return validationCheckResult;
}

var hasRequiredTableValidationTools;

function requireTableValidationTools () {
	if (hasRequiredTableValidationTools) return tableValidationTools;
	hasRequiredTableValidationTools = 1;
	Object.defineProperty(tableValidationTools, "__esModule", { value: true });
	tableValidationTools.TableValidationTools = void 0;
	const __1 = requireDist$1();
	const validation_check_result_1 = requireValidationCheckResult();
	class TableValidationTools {
	    static checkValueSIParseability(entries, propertyName, characteristicType, options) {
	        const { ignoreEmpty = false } = options ?? {};
	        const failures = [];
	        const siDefinition = __1.BoundaryCharacteristicSIDefinitions[characteristicType];
	        entries.forEach((entry, i) => {
	            const value = entry[propertyName];
	            if (ignoreEmpty && (value === null || value === undefined || value === '')) {
	                return;
	            }
	            if (typeof value === 'string' || typeof value === 'number') {
	                try {
	                    const parsed = __1.Utils.parseNumericalSIValue(value, siDefinition);
	                    if (parsed === undefined) {
	                        failures.push({
	                            index: i,
	                            message: `Failed to parse value '${value}' of property '${String(propertyName)}' - returned undefined.`,
	                        });
	                    }
	                }
	                catch (error) {
	                    const errorMessage = error instanceof Error ? error.message : String(error);
	                    failures.push({
	                        index: i,
	                        message: `Failed to parse value '${value}' of property '${String(propertyName)}' using parseNumericalSIValue.`,
	                        details: errorMessage,
	                    });
	                }
	            }
	            else if (value !== null && value !== undefined) {
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' has invalid type '${typeof value}' (expected string or number).`,
	                });
	            }
	        });
	        return failures;
	    }
	    static checkValueBelongsToSet(entries, propertyName, allowedValues, options) {
	        const { ignoreEmpty = false } = options ?? {};
	        const failures = [];
	        entries.forEach((entry, i) => {
	            const value = entry[propertyName];
	            if (ignoreEmpty && (value === null || value === undefined)) {
	                return;
	            }
	            if (!allowedValues.has(value)) {
	                const allowedValuesStr = Array.from(allowedValues)
	                    .map((v) => JSON.stringify(v))
	                    .join(', ');
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' has value ${JSON.stringify(value)} which is not in the allowed set.`,
	                    details: `Set: [${allowedValuesStr}]`,
	                });
	            }
	        });
	        return failures;
	    }
	    static checkValueIsValidSignal(entries, propertyName, signals) {
	        const isStringArray = (arr) => {
	            return arr.length === 0 || typeof arr[0] === 'string';
	        };
	        const signalNames = isStringArray(signals) ? signals : signals.map((s) => s.name);
	        const signalNamesSet = new Set(signalNames);
	        const failures = this.checkValueBelongsToSet(entries, propertyName, signalNamesSet, { ignoreEmpty: false });
	        return failures.map((failure) => {
	            const entry = entries[failure.index];
	            const value = entry[propertyName];
	            return {
	                ...failure,
	                message: `Property '${String(propertyName)}' has value '${value}' which is not an allowed signal`,
	            };
	        });
	    }
	    static checkValueIsNotEmpty(entries, propertyName) {
	        const failures = [];
	        entries.forEach((entry, i) => {
	            const value = entry[propertyName];
	            if (value === null || value === undefined || value === '') {
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' is empty.`,
	                });
	            }
	        });
	        return failures;
	    }
	    static checkListPropertyHasMinimumEntryCount(entries, propertyName, minEntries = 1) {
	        const failures = [];
	        entries.forEach((entry, i) => {
	            const value = entry[propertyName];
	            if (value === null || value === undefined) {
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' is null or undefined but requires at least ${minEntries} entries.`,
	                });
	                return;
	            }
	            if (!Array.isArray(value)) {
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' has invalid type '${typeof value}' (expected array).`,
	                });
	                return;
	            }
	            if (value.length < minEntries) {
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' has ${value.length} entries but requires at least ${minEntries}.`,
	                });
	            }
	        });
	        return failures;
	    }
	    static checkPropertyValuesDiffer(entries, propertyName1, propertyName2) {
	        const failures = [];
	        entries.forEach((entry, i) => {
	            const value1 = entry[propertyName1];
	            const value2 = entry[propertyName2];
	            if (value1 === value2) {
	                failures.push({
	                    index: i,
	                    message: `Properties '${String(propertyName1)}' and '${String(propertyName2)}' have the same value: ${JSON.stringify(value1)}.`,
	                });
	            }
	        });
	        return failures;
	    }
	    static checkValueIsNotInSet(entries, propertyName, forbiddenValues, options) {
	        const { ignoreEmpty = false } = options ?? {};
	        const failures = [];
	        entries.forEach((entry, i) => {
	            const value = entry[propertyName];
	            if (ignoreEmpty && (value === null || value === undefined)) {
	                return;
	            }
	            if (forbiddenValues.has(value)) {
	                const forbiddenValuesStr = Array.from(forbiddenValues)
	                    .map((v) => JSON.stringify(v))
	                    .join(', ');
	                failures.push({
	                    index: i,
	                    message: `Property '${String(propertyName)}' has value ${JSON.stringify(value)} which is in the forbidden set.`,
	                    details: `Forbidden set: [${forbiddenValuesStr}]`,
	                });
	            }
	        });
	        return failures;
	    }
	    static checkForDuplicateEntries(entries) {
	        const failures = [];
	        const hashToFirstIndex = new Map();
	        entries.forEach((entry, i) => {
	            const sortedEntry = __1.Utils.sortObjectKeysRecursively(entry);
	            const hash = JSON.stringify(sortedEntry);
	            if (hashToFirstIndex.has(hash)) {
	                failures.push({
	                    index: i,
	                    message: 'Duplicate entry found.',
	                    details: `Duplicate entry: ${hash}`,
	                });
	            }
	            else {
	                hashToFirstIndex.set(hash, i);
	            }
	        });
	        return failures;
	    }
	    static applyValidationCheck(entries, validationCallable, validationErrorType, options) {
	        const { dropFailingEntries = false, logger } = options;
	        let failures;
	        try {
	            failures = validationCallable(entries);
	        }
	        catch (error) {
	            logger.error('ApplyValidationCheck_UnexpectedError', {
	                validationErrorType,
	                error: error instanceof Error ? error.message : String(error),
	                stack: error instanceof Error ? error.stack : undefined,
	            });
	            return {
	                validatedEntries: entries,
	                validationResults: [
	                    {
	                        type: validationErrorType,
	                        result: validation_check_result_1.ValidationCheckResultStatus.fail,
	                        message: `Validation check encountered an unexpected error: ${error instanceof Error ? error.message : String(error)}`,
	                    },
	                ],
	            };
	        }
	        if (failures.length === 0) {
	            return {
	                validatedEntries: entries,
	                validationResults: [
	                    {
	                        type: validationErrorType,
	                        result: validation_check_result_1.ValidationCheckResultStatus.pass,
	                        message: 'All entries passed validation',
	                    },
	                ],
	            };
	        }
	        const validationResults = [];
	        const failedIndices = new Set();
	        for (const failure of failures) {
	            logger.warn('ApplyValidationCheck_ValidationFailure', {
	                validationErrorType,
	                index: failure.index,
	                message: failure.message,
	                details: failure.details,
	            });
	            if (dropFailingEntries) {
	                logger.warn('ApplyValidationCheck_DroppedEntry', {
	                    validationErrorType,
	                    index: failure.index,
	                    entry: JSON.stringify(entries[failure.index]),
	                });
	                failedIndices.add(failure.index);
	            }
	            validationResults.push({
	                type: validationErrorType,
	                result: validation_check_result_1.ValidationCheckResultStatus.fail,
	                message: failure.message,
	            });
	        }
	        const validatedEntries = dropFailingEntries ? entries.filter((_, index) => !failedIndices.has(index)) : entries;
	        return {
	            validatedEntries,
	            validationResults,
	        };
	    }
	}
	tableValidationTools.TableValidationTools = TableValidationTools;
	
	return tableValidationTools;
}

var ComponentValidation = {};

var SignalValidation = {};

var hasRequiredSignalValidation;

function requireSignalValidation () {
	if (hasRequiredSignalValidation) return SignalValidation;
	hasRequiredSignalValidation = 1;
	Object.defineProperty(SignalValidation, "__esModule", { value: true });
	SignalValidation.validateSignal = validateSignal;
	SignalValidation.validateSignalsFloatingBehavior = validateSignalsFloatingBehavior;
	const BoundaryLevel_1 = requireBoundaryLevel();
	const ComponentValidationResult_1 = requireComponentValidationResult();
	function validateSignal(signal) {
	    const errors = [];
	    const attributes = signal.attributes ?? {};
	    if (attributes.noConnect && Object.values(attributes).filter((attr) => attr).length > 1) {
	        errors.push({
	            type: ComponentValidationResult_1.ComponentValidationErrorType.InternalConsistencyError,
	            message: `Signal '${signal.name}': noConnect cannot be set with any other attribute`,
	            severity: BoundaryLevel_1.BoundaryLevel.Error,
	            locator: { signalId: signal.id, propertyKey: 'attributes.noConnect' },
	        });
	    }
	    if (attributes.power && attributes.ground) {
	        errors.push({
	            type: ComponentValidationResult_1.ComponentValidationErrorType.InternalConsistencyError,
	            message: `Signal '${signal.name}': power and ground cannot both be set`,
	            severity: BoundaryLevel_1.BoundaryLevel.Error,
	            locator: { signalId: signal.id, propertyKey: 'attributes' },
	        });
	    }
	    if (attributes.digital && !signal.supportedDriveModes?.length) {
	        errors.push({
	            type: ComponentValidationResult_1.ComponentValidationErrorType.InternalConsistencyError,
	            message: `Signal '${signal.name}': Digital signal must have at least one drive mode specified`,
	            severity: BoundaryLevel_1.BoundaryLevel.Error,
	            locator: { signalId: signal.id, propertyKey: 'supportedDriveModes' },
	        });
	    }
	    if (!attributes.digital && signal.supportedDriveModes?.length) {
	        errors.push({
	            type: ComponentValidationResult_1.ComponentValidationErrorType.InternalConsistencyError,
	            message: `Signal '${signal.name}': Drive modes may only be set on digital signals`,
	            severity: BoundaryLevel_1.BoundaryLevel.Error,
	            locator: { signalId: signal.id, propertyKey: 'supportedDriveModes' },
	        });
	    }
	    if (attributes.activeLow && !attributes.digital) {
	        errors.push({
	            type: ComponentValidationResult_1.ComponentValidationErrorType.InternalConsistencyError,
	            message: `Signal '${signal.name}': activeLow may only be set on digital signals`,
	            severity: BoundaryLevel_1.BoundaryLevel.Error,
	            locator: { signalId: signal.id, propertyKey: 'attributes.activeLow' },
	        });
	    }
	    return errors;
	}
	const FLOATING_BEHAVIOR_EXEMPT_SIGNAL_COUNT = 2;
	function validateSignalsFloatingBehavior(signals) {
	    if (signals.length <= FLOATING_BEHAVIOR_EXEMPT_SIGNAL_COUNT) {
	        return [];
	    }
	    const errors = [];
	    for (const signal of signals) {
	        if (!signal.isCadstromInternal && signal.floatingBehavior === undefined) {
	            errors.push({
	                type: ComponentValidationResult_1.ComponentValidationErrorType.InternalConsistencyError,
	                message: `Signal '${signal.name}': floating behaviour must be specified`,
	                severity: BoundaryLevel_1.BoundaryLevel.Error,
	                locator: { signalId: signal.id, propertyKey: 'floatingBehavior' },
	            });
	        }
	    }
	    return errors;
	}
	
	return SignalValidation;
}

var hasRequiredComponentValidation;

function requireComponentValidation () {
	if (hasRequiredComponentValidation) return ComponentValidation;
	hasRequiredComponentValidation = 1;
	(function (exports) {
		var __createBinding = (ComponentValidation && ComponentValidation.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ComponentValidation && ComponentValidation.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireSignalValidation(), exports);
		
	} (ComponentValidation));
	return ComponentValidation;
}

var hasRequiredValidationChecks;

function requireValidationChecks () {
	if (hasRequiredValidationChecks) return ValidationChecks;
	hasRequiredValidationChecks = 1;
	(function (exports) {
		var __createBinding = (ValidationChecks && ValidationChecks.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ValidationChecks && ValidationChecks.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireTableValidationTools(), exports);
		__exportStar(requireValidationCheckResult(), exports);
		__exportStar(requireComponentValidation(), exports);
		
	} (ValidationChecks));
	return ValidationChecks;
}

var types$3 = {};

var NetDriveCompatibilityRequirementInstanceMetadata = {};

var hasRequiredNetDriveCompatibilityRequirementInstanceMetadata;

function requireNetDriveCompatibilityRequirementInstanceMetadata () {
	if (hasRequiredNetDriveCompatibilityRequirementInstanceMetadata) return NetDriveCompatibilityRequirementInstanceMetadata;
	hasRequiredNetDriveCompatibilityRequirementInstanceMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetDriveCompatibilityRequirementInstanceMetadataSchema = exports.NetDriveCompatibilityValidationResultKey = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.NetDriveCompatibilityValidationResultKey = 'NetDriveCompatibilityResult';
		exports.NetDriveCompatibilityRequirementInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.NetDriveCompatibilityValidationResultKey),
		    netName: zod_1.z.string(),
		    netStatsDepId: zod_1.z.string().optional(),
		});
		
	} (NetDriveCompatibilityRequirementInstanceMetadata));
	return NetDriveCompatibilityRequirementInstanceMetadata;
}

var NetFunctionalNameRequirement = {};

var hasRequiredNetFunctionalNameRequirement;

function requireNetFunctionalNameRequirement () {
	if (hasRequiredNetFunctionalNameRequirement) return NetFunctionalNameRequirement;
	hasRequiredNetFunctionalNameRequirement = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetFunctionalNameRequirementResultMetadataSchema = exports.NetFunctionalNameRequirementInstanceMetadataSchema = exports.NetFunctionalNameRequirementResultTypeName = exports.NetFunctionalNameValidationResultKey = exports.NetFunctionalNameValidationRequirementTypeId = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const IInsightError_1 = requireIInsightError();
		exports.NetFunctionalNameValidationRequirementTypeId = 'NetFunctionalName';
		exports.NetFunctionalNameValidationResultKey = 'NetFunctionalNameValidationResult';
		exports.NetFunctionalNameRequirementResultTypeName = 'NetFunctionalNameResult';
		exports.NetFunctionalNameRequirementInstanceMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.NetFunctionalNameValidationResultKey),
		    netName: zod_1.z.string(),
		});
		exports.NetFunctionalNameRequirementResultMetadataSchema = zod_1.z.object({
		    type: zod_1.z.literal(exports.NetFunctionalNameRequirementResultTypeName),
		    insights: zod_1.z.lazy(() => IInsightError_1.IInsightErrorSchema).array(),
		});
		
	} (NetFunctionalNameRequirement));
	return NetFunctionalNameRequirement;
}

var hasRequiredTypes$3;

function requireTypes$3 () {
	if (hasRequiredTypes$3) return types$3;
	hasRequiredTypes$3 = 1;
	(function (exports) {
		var __createBinding = (types$3 && types$3.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (types$3 && types$3.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireComponentFunctionRequirementInstanceMetadata(), exports);
		__exportStar(requireComponentResourceRequirementInstanceMetadata(), exports);
		__exportStar(requireComponentLimitsValidationInstanceMetadata(), exports);
		__exportStar(requireDesignObject(), exports);
		__exportStar(requireExpressionVariableValues(), exports);
		__exportStar(requireLogicalBusValidationInstanceMetadata(), exports);
		__exportStar(requireModuleSignalDriveMode(), exports);
		__exportStar(requireNetDriveCompatibilityRequirementInstanceMetadata(), exports);
		__exportStar(requireNetDriveCompatibilityRequirementInstanceResultMetadataSchema(), exports);
		__exportStar(requireNetDriveCompatibilityRequirementResultDetails(), exports);
		__exportStar(requireNetFunctionalNameRequirement(), exports);
		__exportStar(requirePinoutValidationInstanceMetadata(), exports);
		__exportStar(requireComponentValueValidationInstanceMetadata(), exports);
		__exportStar(requireRequirementInstanceNote(), exports);
		__exportStar(requireRequirementInstanceResult(), exports);
		__exportStar(requireRequirementInstanceResultOverrideStatus(), exports);
		__exportStar(requireRequirementInstanceResults(), exports);
		__exportStar(requireRequirementInstanceResultStatus(), exports);
		
	} (types$3));
	return types$3;
}

var hasRequiredDist$1;

function requireDist$1 () {
	if (hasRequiredDist$1) return dist$1;
	hasRequiredDist$1 = 1;
	(function (exports) {
		var __createBinding = (dist$1 && dist$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (dist$1 && dist$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.normalizeSiString = exports.RequirementsV2 = exports.Mutex = exports.Markdown = exports.Files = exports.Utils = exports.SPICE = exports.ModuleResolution = exports.Migration = exports.FunctionalModels = exports.DesignModels = exports.Common = exports.AnalysisTools = void 0;
		exports.AnalysisTools = requireAnalysis$1();
		exports.Common = requireCommon$3();
		exports.DesignModels = requireDesignModels();
		exports.FunctionalModels = requireFunctionalModels();
		exports.Migration = requireMigration();
		exports.ModuleResolution = requireModuleResolution();
		__exportStar(requireModeStateConfigGeneration(), exports);
		exports.SPICE = requireSPICE();
		exports.Utils = requireUtils$4();
		exports.Files = requireFiles();
		exports.Markdown = requireMarkdown();
		exports.Mutex = requireMutex();
		exports.RequirementsV2 = requireRequirementsV2$1();
		__exportStar(requireAI(), exports);
		__exportStar(requireAnalysisRequirements(), exports);
		__exportStar(requireBoundaryAnalysisMode(), exports);
		__exportStar(requireBoundaryAssertionStrength(), exports);
		__exportStar(requireBoundaryCharacteristic(), exports);
		__exportStar(requireBoundaryRole(), exports);
		__exportStar(requireBoundingBox(), exports);
		__exportStar(requireMappedBoundaries(), exports);
		__exportStar(requireBoundaryTarget(), exports);
		__exportStar(requireBoundaryLevel(), exports);
		__exportStar(requireCharacteristicExpressionParser(), exports);
		__exportStar(requireCircuitStates(), exports);
		__exportStar(requireTypes$5(), exports);
		__exportStar(requireCommonParameterUnits(), exports);
		__exportStar(requireComponentBoundary(), exports);
		__exportStar(requireErrors$1(), exports);
		__exportStar(requireComponentModelParameterDefinition(), exports);
		__exportStar(requireComponentValidationResult(), exports);
		__exportStar(requireDesignConfiguration(), exports);
		__exportStar(requireDesignModels(), exports);
		__exportStar(requireElectricalSchematic(), exports);
		__exportStar(requireTypes$4(), exports);
		__exportStar(requireIElectricalSchematicTraversalHistory(), exports);
		__exportStar(requireIInsightError(), exports);
		__exportStar(requireISubModelSignalMapItemBase(), exports);
		__exportStar(requireIModelInfoBase(), exports);
		__exportStar(requireILogger(), exports);
		__exportStar(requireInferenceReports(), exports);
		__exportStar(requireInferenceReportUtil(), exports);
		__exportStar(requireMergeInferenceReports(), exports);
		__exportStar(requireMessageResult(), exports);
		__exportStar(requireMigration(), exports);
		__exportStar(requireNumericalBoundary(), exports);
		__exportStar(requireRelativeSignal(), exports);
		__exportStar(requireSIUnitDeclaration(), exports);
		__exportStar(requireValueAndExpressionParsing(), exports);
		__exportStar(requireValidationChecks(), exports);
		__exportStar(requireVarianceTypes(), exports);
		__exportStar(requireFileCollectionValidationTypes(), exports);
		__exportStar(requireTypes$3(), exports);
		__exportStar(requireModelBoundingBoxUtils(), exports);
		__exportStar(requireNamedCallStack(), exports);
		__exportStar(requireParsedCallStack(), exports);
		__exportStar(requireChainableIterator(), exports);
		__exportStar(requireIterableMap(), exports);
		__exportStar(requireSafeMap(), exports);
		__exportStar(requireToArray(), exports);
		__exportStar(requireZodOutputType(), exports);
		var normalize_si_string_1 = requireNormalizeSiString();
		Object.defineProperty(exports, "normalizeSiString", { enumerable: true, get: function () { return normalize_si_string_1.normalizeSiString; } });
		
	} (dist$1));
	return dist$1;
}

var hasRequiredTriggers;

function requireTriggers () {
	if (hasRequiredTriggers) return triggers;
	hasRequiredTriggers = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AnalysisTriggerExecutionSchema = exports.AnalysisTriggerSchema = exports.analysisTriggerAccessors = exports.analysisTriggerFilterSchema = exports.AnalysisTriggerFilterDefinitionSchema = exports.AnalysisTriggerFilterFindingContinuitySchema = exports.AnalysisTriggerFilterFunctionSchema = exports.AnalysisTriggerContextSchema = exports.AnalysisTriggerInvokerSchema = exports.AnalysisTriggerSubjectDefinitionSchema = exports.AnalysisTriggerSubjectRefSchema = exports.AnalysisTriggerLoadedSubjectSchema = exports.AnalysisTriggerSubjectIdentitySchema = exports.AnalysisTriggerSubjectSchema = exports.AnalysisTriggerFindingRefSchema = exports.AnalysisTriggerPackageSubjectDataSchema = exports.AnalysisTriggerPackageModuleRefSchema = exports.AnalysisTriggerModuleBomSnapshotSchema = exports.AnalysisTriggerSubjectCustomTypeDefinitionSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const QueryBuilder_1 = requireQueryBuilder();
		const agent_executor_1 = requireAgentExecutor();
		const lifecycle_1 = requireLifecycle();
		const trigger_executor_1 = requireTriggerExecutor();
		exports.AnalysisTriggerSubjectCustomTypeDefinitionSchema = zod_1.default.object({
		    customTypeName: zod_1.default.string(),
		    subjectName: zod_1.default.string(),
		    description: zod_1.default.string(),
		    properties: zod_1.default.record(zod_1.default.string(), zod_1.default.unknown()).optional(),
		});
		exports.AnalysisTriggerModuleBomSnapshotSchema = zod_1.default.object({
		    bomItemId: zod_1.default.string(),
		    moduleId: zod_1.default.string().nullable().optional(),
		    referenceDesignator: zod_1.default.string(),
		    isReviewed: zod_1.default.boolean(),
		    resolutionType: zod_1.default.enum(['sigmaModel', 'packageStore', 'nonElectrical']).nullable().optional(),
		    sigmaModelType: zod_1.default.string().nullable().optional(),
		    packageId: zod_1.default.string().nullable().optional(),
		    parameters: zod_1.default.unknown().optional(),
		    signalMap: zod_1.default.record(zod_1.default.string(), zod_1.default.unknown()).optional(),
		    isDnp: zod_1.default.boolean().nullable().optional(),
		    manufacturerPartNumber: zod_1.default.string().nullable().optional(),
		    manufacturerId: zod_1.default.string().nullable().optional(),
		    manufacturerName: zod_1.default.string().nullable().optional(),
		    datasheetFileId: zod_1.default.string().nullable().optional(),
		    moduleDatasheetFileId: zod_1.default.string().nullable().optional(),
		    moduleDatasheetUrl: zod_1.default.string().nullable().optional(),
		    packageUnderstood: zod_1.default.boolean().nullable().optional(),
		    packagePinCount: zod_1.default.number().int().nonnegative().nullable().optional(),
		});
		exports.AnalysisTriggerPackageModuleRefSchema = zod_1.default.object({
		    bomItemId: zod_1.default.string(),
		    moduleId: zod_1.default.string().nullable(),
		    referenceDesignator: zod_1.default.string(),
		});
		exports.AnalysisTriggerPackageSubjectDataSchema = zod_1.default.object({
		    packageId: zod_1.default.string(),
		    sigmaModelType: zod_1.default.string(),
		    modules: zod_1.default.array(exports.AnalysisTriggerPackageModuleRefSchema),
		});
		exports.AnalysisTriggerFindingRefSchema = zod_1.default.object({
		    findingId: zod_1.default.string(),
		});
		exports.AnalysisTriggerSubjectSchema = zod_1.default.discriminatedUnion('typeName', [
		    zod_1.default.object({
		        typeName: zod_1.default.literal('module'),
		        data: zod_1.default.object({
		            module: sigma_engine_shared_1.DesignModels.ProjectModuleSchema,
		            instances: zod_1.default.array(sigma_engine_shared_1.DesignModels.ModuleSchema),
		            bomItem: exports.AnalysisTriggerModuleBomSnapshotSchema,
		        }),
		    }),
		    zod_1.default.object({ typeName: zod_1.default.literal('net'), data: sigma_engine_shared_1.DesignModels.NetInfoSchema }),
		    zod_1.default.object({ typeName: zod_1.default.literal('schematic'), data: sigma_engine_shared_1.DesignModels.SchematicModelSchema }),
		    zod_1.default.object({ typeName: zod_1.default.literal('project'), data: sigma_engine_shared_1.DesignModels.ProjectModelSchema }),
		    zod_1.default.object({ typeName: zod_1.default.literal('package'), data: exports.AnalysisTriggerPackageSubjectDataSchema }),
		    zod_1.default.object({
		        typeName: zod_1.default.literal('packageStorePageModule'),
		        packageId: zod_1.default.string(),
		        data: zod_1.default.object({ module: sigma_engine_shared_1.DesignModels.ProjectModuleSchema, instances: zod_1.default.array(sigma_engine_shared_1.DesignModels.ModuleSchema) }),
		    }),
		    zod_1.default.object({
		        typeName: zod_1.default.literal('custom'),
		        customTypeName: zod_1.default.string(),
		        data: exports.AnalysisTriggerSubjectCustomTypeDefinitionSchema,
		    }),
		    zod_1.default.object({
		        typeName: zod_1.default.literal('finding'),
		        data: exports.AnalysisTriggerFindingRefSchema,
		    }),
		]);
		exports.AnalysisTriggerSubjectIdentitySchema = zod_1.default.object({
		    subjectType: zod_1.default.enum([
		        'module',
		        'net',
		        'schematic',
		        'project',
		        'package',
		        'packageStorePageModule',
		        'custom',
		        'finding',
		    ]),
		    subjectKey: zod_1.default.string(),
		    subjectFingerprint: zod_1.default.string(),
		});
		exports.AnalysisTriggerLoadedSubjectSchema = zod_1.default.object({
		    identity: exports.AnalysisTriggerSubjectIdentitySchema,
		    subject: exports.AnalysisTriggerSubjectSchema,
		});
		exports.AnalysisTriggerSubjectRefSchema = zod_1.default.object({
		    subjectType: exports.AnalysisTriggerSubjectIdentitySchema.shape.subjectType,
		    subjectKey: zod_1.default.string(),
		});
		exports.AnalysisTriggerSubjectDefinitionSchema = zod_1.default.discriminatedUnion('typeName', [
		    zod_1.default.object({ typeName: zod_1.default.literal('module') }),
		    zod_1.default.object({ typeName: zod_1.default.literal('net') }),
		    zod_1.default.object({ typeName: zod_1.default.literal('schematic') }),
		    zod_1.default.object({ typeName: zod_1.default.literal('project') }),
		    zod_1.default.object({ typeName: zod_1.default.literal('package') }),
		    zod_1.default.object({ typeName: zod_1.default.literal('packageStorePageModule'), packageId: zod_1.default.string() }),
		    zod_1.default.object({ typeName: zod_1.default.literal('custom'), customTypeName: zod_1.default.string() }),
		    zod_1.default.object({ typeName: zod_1.default.literal('finding') }),
		]);
		exports.AnalysisTriggerInvokerSchema = zod_1.default.discriminatedUnion('kind', [
		    zod_1.default.object({ kind: zod_1.default.literal('user'), userId: zod_1.default.string() }),
		    zod_1.default.object({ kind: zod_1.default.literal('system'), reason: zod_1.default.string() }),
		]);
		exports.AnalysisTriggerContextSchema = zod_1.default
		    .object({
		    projectId: zod_1.default.string().optional(),
		    buildId: zod_1.default.string().optional(),
		    projectModelId: zod_1.default.string().optional(),
		    ownerId: zod_1.default.string().optional(),
		    invoker: exports.AnalysisTriggerInvokerSchema,
		    analysisRunId: zod_1.default.string().optional(),
		})
		    .passthrough();
		exports.AnalysisTriggerFilterFunctionSchema = zod_1.default.object({
		    type: zod_1.default.literal('func'),
		    func: zod_1.default.custom((value) => typeof value === 'function'),
		});
		exports.AnalysisTriggerFilterFindingContinuitySchema = zod_1.default.object({
		    type: zod_1.default.literal('finding_continuity'),
		});
		exports.AnalysisTriggerFilterDefinitionSchema = zod_1.default.discriminatedUnion('type', [
		    exports.AnalysisTriggerFilterFunctionSchema,
		    exports.AnalysisTriggerFilterFindingContinuitySchema,
		]);
		const EXACT_STRING_FILTER_OPERATORS = ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual'];
		const OPTIONAL_EXACT_STRING_FILTER_OPERATORS = [
		    ...EXACT_STRING_FILTER_OPERATORS,
		    'isEmpty',
		    'isNotEmpty',
		];
		const TEXT_ID_FILTER_OPERATORS = [
		    ...EXACT_STRING_FILTER_OPERATORS,
		    'contains',
		    'startsWith',
		    'endsWith',
		    'isEmpty',
		    'isNotEmpty',
		];
		exports.analysisTriggerFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
		    fields: {
		        triggerId: {
		            description: 'Trigger id.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: EXACT_STRING_FILTER_OPERATORS,
		        },
		        lifecycleStage: {
		            description: 'Lifecycle stage: package-understanding, pre-analysis, analysis, or post-analysis.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: EXACT_STRING_FILTER_OPERATORS,
		        },
		        runPolicy: {
		            description: 'When the trigger runs: automatic (every analysis pass) or on_demand (only runs that include on-demand triggers).',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: EXACT_STRING_FILTER_OPERATORS,
		        },
		        subjectType: {
		            description: 'Subject type the trigger runs against: module, net, schematic, project, package, packageStorePageModule, custom, or finding.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: EXACT_STRING_FILTER_OPERATORS,
		        },
		        customTypeName: {
		            description: 'Custom subject type name, only present when subjectType is custom.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: OPTIONAL_EXACT_STRING_FILTER_OPERATORS,
		        },
		        packageId: {
		            description: 'Package id, only present when subjectType is packageStorePageModule.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: OPTIONAL_EXACT_STRING_FILTER_OPERATORS,
		        },
		        executorType: {
		            description: 'Trigger executor type, such as agent.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: EXACT_STRING_FILTER_OPERATORS,
		        },
		        agentId: {
		            description: 'Agent id for agent-backed triggers, such as cadstrom-project-assistant.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: TEXT_ID_FILTER_OPERATORS,
		        },
		        filterType: {
		            description: 'Trigger filter type, such as func. Empty when the trigger has no filter.',
		            options: { type: QueryBuilder_1.FieldType.String },
		            allowedOperators: OPTIONAL_EXACT_STRING_FILTER_OPERATORS,
		        },
		        hasFilter: {
		            description: 'True when the trigger has a subject filter.',
		            options: { type: QueryBuilder_1.FieldType.Boolean },
		        },
		    },
		});
		exports.analysisTriggerAccessors = {
		    triggerId: (trigger) => trigger.triggerId,
		    lifecycleStage: (trigger) => trigger.lifecycleStage,
		    runPolicy: (trigger) => trigger.runPolicy,
		    subjectType: (trigger) => trigger.subjectDefinition.typeName,
		    customTypeName: (trigger) => trigger.subjectDefinition.typeName === 'custom' ? trigger.subjectDefinition.customTypeName : null,
		    packageId: (trigger) => trigger.subjectDefinition.typeName === 'packageStorePageModule' ? trigger.subjectDefinition.packageId : null,
		    executorType: (trigger) => trigger.executor.executorType,
		    agentId: (trigger) => trigger.executor.executorType === agent_executor_1.AGENT_EXECUTOR_TYPE ? trigger.executor.agentId : null,
		    filterType: (trigger) => trigger.filter?.type ?? null,
		    hasFilter: (trigger) => trigger.filter !== undefined,
		};
		exports.AnalysisTriggerSchema = zod_1.default.object({
		    triggerId: zod_1.default.string(),
		    lifecycleStage: lifecycle_1.AnalysisLifecycleStageSchema,
		    runPolicy: lifecycle_1.AnalysisTriggerRunPolicySchema,
		    subjectDefinition: exports.AnalysisTriggerSubjectDefinitionSchema,
		    filter: exports.AnalysisTriggerFilterDefinitionSchema.optional(),
		    executor: trigger_executor_1.TriggerExecutorSchema.passthrough(),
		});
		exports.AnalysisTriggerExecutionSchema = zod_1.default.object({
		    executionId: zod_1.default.string(),
		    triggerId: zod_1.default.string(),
		    subject: exports.AnalysisTriggerSubjectSchema,
		    subjectIdentity: exports.AnalysisTriggerSubjectIdentitySchema.optional(),
		    context: exports.AnalysisTriggerContextSchema,
		});
		
	} (triggers));
	return triggers;
}

var hasRequiredAnalysis;

function requireAnalysis () {
	if (hasRequiredAnalysis) return Analysis$1;
	hasRequiredAnalysis = 1;
	(function (exports) {
		var __createBinding = (Analysis$1 && Analysis$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Analysis$1 && Analysis$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAgentDefinition(), exports);
		__exportStar(requireBusSubject(), exports);
		__exportStar(requireCustomSubject(), exports);
		__exportStar(requireDiffPairSubject(), exports);
		__exportStar(requireAgentExecutor(), exports);
		__exportStar(requireHybridExecutor(), exports);
		__exportStar(requireLifecycle(), exports);
		__exportStar(requireTriggerExecutor(), exports);
		__exportStar(requireTriggers(), exports);
		
	} (Analysis$1));
	return Analysis$1;
}

var AnalysisResult = {};

var analysisResultFilterSchema = {};

var hasRequiredAnalysisResultFilterSchema;

function requireAnalysisResultFilterSchema () {
	if (hasRequiredAnalysisResultFilterSchema) return analysisResultFilterSchema;
	hasRequiredAnalysisResultFilterSchema = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.analysisResultFilterSchema = exports.statusDisplayToDb = void 0;
		exports.isValidOverrideStatus = isValidOverrideStatus;
		const sigma_engine_shared_1 = requireDist$1();
		const QueryBuilder_1 = requireQueryBuilder();
		exports.statusDisplayToDb = Object.fromEntries(Object.entries(sigma_engine_shared_1.RequirementInstanceResultStatus).map(([key, value]) => [key, value]));
		function resolveStatusValue(displayValue) {
		    const dbValue = exports.statusDisplayToDb[displayValue];
		    if (dbValue !== undefined) {
		        return dbValue;
		    }
		    if (Object.values(exports.statusDisplayToDb).includes(displayValue)) {
		        return displayValue;
		    }
		    throw new Error(`Unknown status display value: "${displayValue}"`);
		}
		function isValidOverrideStatus(status) {
		    return (status === sigma_engine_shared_1.RequirementInstanceResultStatus.Error ||
		        status === sigma_engine_shared_1.RequirementInstanceResultStatus.Warning ||
		        status === sigma_engine_shared_1.RequirementInstanceResultStatus.Success);
		}
		function buildComputedStatusWhere(operator, value) {
		    if (value === undefined || value === '') {
		        if (operator !== 'isEmpty' && operator !== 'isNotEmpty') {
		            return {};
		        }
		    }
		    switch (operator) {
		        case 'is':
		        case 'equals': {
		            const dbValue = resolveStatusValue(value);
		            return {
		                OR: [
		                    ...(isValidOverrideStatus(dbValue) ? [{ statusOverride: dbValue }] : []),
		                    { AND: [{ statusOverride: null }, { status: dbValue }] },
		                ],
		            };
		        }
		        case 'not':
		        case 'doesNotEqual': {
		            const dbValue = resolveStatusValue(value);
		            return {
		                AND: [
		                    ...(isValidOverrideStatus(dbValue) ? [{ statusOverride: { not: { equals: dbValue } } }] : []),
		                    {
		                        OR: [{ statusOverride: { not: null } }, { status: { not: { equals: dbValue } } }],
		                    },
		                ],
		            };
		        }
		        case 'isAnyOf': {
		            const values = (0, QueryBuilder_1.parseIsAnyOfValues)(value);
		            const enumValues = values.map(resolveStatusValue).filter(Boolean);
		            if (enumValues.length === 0) {
		                return {};
		            }
		            const orConditions = enumValues.flatMap((dbValue) => [
		                ...(isValidOverrideStatus(dbValue) ? [{ statusOverride: dbValue }] : []),
		                { AND: [{ statusOverride: null }, { status: dbValue }] },
		            ]);
		            return { OR: orConditions };
		        }
		        default:
		            return {};
		    }
		}
		exports.analysisResultFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
		    fields: {
		        requirementId: { options: { type: QueryBuilder_1.FieldType.String, nonNullable: true } },
		        typeId: { options: { type: QueryBuilder_1.FieldType.String, nonNullable: true } },
		        expected: { options: { type: QueryBuilder_1.FieldType.String, nonNullable: true } },
		        actual: { options: { type: QueryBuilder_1.FieldType.String, nonNullable: true } },
		        status: {
		            options: {
		                type: QueryBuilder_1.FieldType.String,
		                values: exports.statusDisplayToDb,
		            },
		        },
		        computedStatus: {
		            options: {
		                type: QueryBuilder_1.FieldType.Custom,
		                buildWhere: buildComputedStatusWhere,
		                buildOrderBy: (direction) => [{ statusOverride: direction }, { status: direction }],
		            },
		        },
		        originalStatus: {
		            options: {
		                type: QueryBuilder_1.FieldType.String,
		                column: 'status',
		                values: exports.statusDisplayToDb,
		            },
		        },
		        isReviewed: { options: { type: QueryBuilder_1.FieldType.Boolean } },
		        designObject: {
		            options: {
		                type: QueryBuilder_1.FieldType.Relation,
		                relation: 'designObjects',
		                searchColumns: ['net', 'module', 'component', 'signal'],
		            },
		        },
		    },
		    ignoredFields: ['actions'],
		});
		
	} (analysisResultFilterSchema));
	return analysisResultFilterSchema;
}

var analysisType = {};

var hasRequiredAnalysisType;

function requireAnalysisType () {
	if (hasRequiredAnalysisType) return analysisType;
	hasRequiredAnalysisType = 1;
	Object.defineProperty(analysisType, "__esModule", { value: true });
	analysisType.AnalysisTypeSchema = analysisType.AnalysisType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var AnalysisType;
	(function (AnalysisType) {
	    AnalysisType["Pinout"] = "pinout";
	    AnalysisType["ComponentValues"] = "componentValues";
	})(AnalysisType || (analysisType.AnalysisType = AnalysisType = {}));
	analysisType.AnalysisTypeSchema = zod_1.z.nativeEnum(AnalysisType);
	
	return analysisType;
}

var hasRequiredAnalysisResult;

function requireAnalysisResult () {
	if (hasRequiredAnalysisResult) return AnalysisResult;
	hasRequiredAnalysisResult = 1;
	(function (exports) {
		var __createBinding = (AnalysisResult && AnalysisResult.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (AnalysisResult && AnalysisResult.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAnalysisResultFilterSchema(), exports);
		__exportStar(requireAnalysisType(), exports);
		
	} (AnalysisResult));
	return AnalysisResult;
}

var Auth = {};

var authentication_types = {};

var hasRequiredAuthentication_types;

function requireAuthentication_types () {
	if (hasRequiredAuthentication_types) return authentication_types;
	hasRequiredAuthentication_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ActorSchema = exports.MembershipSchema = exports.GroupTypeSchema = exports.GroupType = exports.ActorTypeSchema = exports.ActorType = exports.MembershipRoleSchema = exports.MembershipRole = exports.UserRecordTypeSchema = exports.UserRecordType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		var UserRecordType;
		(function (UserRecordType) {
		    UserRecordType["User"] = "user";
		    UserRecordType["Organization"] = "organization";
		})(UserRecordType || (exports.UserRecordType = UserRecordType = {}));
		exports.UserRecordTypeSchema = zod_1.z.nativeEnum(UserRecordType);
		var MembershipRole;
		(function (MembershipRole) {
		    MembershipRole["Admin"] = "admin";
		    MembershipRole["Member"] = "member";
		    MembershipRole["Trainee"] = "trainee";
		})(MembershipRole || (exports.MembershipRole = MembershipRole = {}));
		exports.MembershipRoleSchema = zod_1.z.nativeEnum(MembershipRole);
		var ActorType;
		(function (ActorType) {
		    ActorType["User"] = "user";
		    ActorType["Organization"] = "organization";
		})(ActorType || (exports.ActorType = ActorType = {}));
		exports.ActorTypeSchema = zod_1.z.nativeEnum(ActorType);
		var GroupType;
		(function (GroupType) {
		    GroupType["Organization"] = "organization";
		})(GroupType || (exports.GroupType = GroupType = {}));
		exports.GroupTypeSchema = zod_1.z.nativeEnum(GroupType);
		exports.MembershipSchema = zod_1.z.object({
		    groupId: zod_1.z.string(),
		    groupName: zod_1.z.string(),
		    groupUsername: zod_1.z.string(),
		    groupType: exports.GroupTypeSchema,
		    role: exports.MembershipRoleSchema,
		});
		exports.ActorSchema = zod_1.z.object({
		    type: exports.ActorTypeSchema,
		    userId: zod_1.z.string(),
		    email: zod_1.z.string(),
		});
		
	} (authentication_types));
	return authentication_types;
}

var editProvenance_types = {};

var hasRequiredEditProvenance_types;

function requireEditProvenance_types () {
	if (hasRequiredEditProvenance_types) return editProvenance_types;
	hasRequiredEditProvenance_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.UNATTENDED_EDIT_PROVENANCE = exports.EditProvenanceSchema = exports.EditAgencySchema = exports.EditAgency = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const authentication_types_1 = requireAuthentication_types();
		var EditAgency;
		(function (EditAgency) {
		    EditAgency["Ui"] = "ui";
		    EditAgency["Mcp"] = "mcp";
		    EditAgency["Agent"] = "agent";
		    EditAgency["Workflow"] = "workflow";
		    EditAgency["System"] = "system";
		})(EditAgency || (exports.EditAgency = EditAgency = {}));
		exports.EditAgencySchema = zod_1.z.nativeEnum(EditAgency);
		exports.EditProvenanceSchema = zod_1.z.object({
		    actorUserId: zod_1.z.string().nullable(),
		    actorType: authentication_types_1.ActorTypeSchema.nullable(),
		    agency: exports.EditAgencySchema,
		});
		exports.UNATTENDED_EDIT_PROVENANCE = {
		    actorUserId: null,
		    actorType: null,
		    agency: EditAgency.System,
		};
		
	} (editProvenance_types));
	return editProvenance_types;
}

var session = {};

var hasRequiredSession;

function requireSession () {
	if (hasRequiredSession) return session;
	hasRequiredSession = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AUTH0_ORG_NAME_CLAIM = exports.AUTH0_ORG_ID_CLAIM = exports.AUTH0_CLAIM_NAMESPACE = exports.STAFF_ORG_USERNAME = void 0;
		exports.isStaff = isStaff;
		exports.assertNoStaffEscalation = assertNoStaffEscalation;
		exports.accessibleOwnerIds = accessibleOwnerIds;
		exports.adminOrgIds = adminOrgIds;
		exports.isReservedOrgUsername = isReservedOrgUsername;
		const authentication_types_1 = requireAuthentication_types();
		exports.STAFF_ORG_USERNAME = 'cadstrom';
		exports.AUTH0_CLAIM_NAMESPACE = 'https://cadstrom.io';
		exports.AUTH0_ORG_ID_CLAIM = `${exports.AUTH0_CLAIM_NAMESPACE}/org_id`;
		exports.AUTH0_ORG_NAME_CLAIM = `${exports.AUTH0_CLAIM_NAMESPACE}/org_name`;
		function isStaff(session) {
		    if (session.actor.type !== authentication_types_1.ActorType.User) {
		        return false;
		    }
		    return session.memberships.some((m) => m.groupUsername === exports.STAFF_ORG_USERNAME);
		}
		function assertNoStaffEscalation(session) {
		    if (session.actor.type === authentication_types_1.ActorType.User || session.abilities === undefined) {
		        return;
		    }
		    for (const [scope, ability] of session.abilities) {
		        for (const rule of ability.rules) {
		            const actions = Array.isArray(rule.action) ? rule.action : [rule.action];
		            const subjects = Array.isArray(rule.subject) ? rule.subject : [rule.subject];
		            const isManageAll = !rule.inverted && actions.includes('manage') && subjects.includes('all');
		            const conditionCount = rule.conditions === undefined ? 0 : Object.keys(rule.conditions).length;
		            if (isManageAll && conditionCount === 0) {
		                throw new Error(`Non-user actor '${session.actor.userId}' has staff-shaped permissions ` +
		                    `(unconditioned manage-all rule in scope "${scope}")`);
		            }
		        }
		    }
		}
		function accessibleOwnerIds(session, options) {
		    const memberships = options?.excludeTrainees === true ?
		        session.memberships.filter((m) => m.role !== authentication_types_1.MembershipRole.Trainee)
		        : session.memberships;
		    return [session.actor.userId, ...memberships.map((m) => m.groupId)];
		}
		function adminOrgIds(session) {
		    return session.memberships.filter((m) => m.role === authentication_types_1.MembershipRole.Admin).map((m) => m.groupId);
		}
		function isReservedOrgUsername(username) {
		    return username.toLowerCase() === exports.STAFF_ORG_USERNAME;
		}
		
	} (session));
	return session;
}

var reviewerRole = {};

var hasRequiredReviewerRole;

function requireReviewerRole () {
	if (hasRequiredReviewerRole) return reviewerRole;
	hasRequiredReviewerRole = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ReviewerRoles = exports.REVIEWER_ROLES_ASCENDING = exports.ReviewerRoleSchema = exports.ReviewerRole = void 0;
		const z = /*@__PURE__*/ requireZod();
		var ReviewerRole;
		(function (ReviewerRole) {
		    ReviewerRole["Senior"] = "senior_reviewer";
		    ReviewerRole["Expert"] = "expert_reviewer";
		})(ReviewerRole || (exports.ReviewerRole = ReviewerRole = {}));
		exports.ReviewerRoleSchema = z.nativeEnum(ReviewerRole);
		exports.REVIEWER_ROLES_ASCENDING = [ReviewerRole.Senior, ReviewerRole.Expert];
		class ReviewerRoles {
		    static rank(role) {
		        return exports.REVIEWER_ROLES_ASCENDING.indexOf(role);
		    }
		    static outranks(role, other) {
		        return ReviewerRoles.rank(role) > ReviewerRoles.rank(other);
		    }
		    static satisfiedBy(role) {
		        if (role === null) {
		            return [];
		        }
		        return exports.REVIEWER_ROLES_ASCENDING.filter((candidate) => !ReviewerRoles.outranks(candidate, role));
		    }
		}
		exports.ReviewerRoles = ReviewerRoles;
		
	} (reviewerRole));
	return reviewerRole;
}

var frontendAuth = {};

var hasRequiredFrontendAuth;

function requireFrontendAuth () {
	if (hasRequiredFrontendAuth) return frontendAuth;
	hasRequiredFrontendAuth = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OWNED_ENTITY_SUBJECTS = void 0;
		exports.buildFrontendAuth = buildFrontendAuth;
		const authentication_types_1 = requireAuthentication_types();
		const session_1 = requireSession();
		exports.OWNED_ENTITY_SUBJECTS = [
		    'analysisResult',
		    'extractionConfig',
		    'file',
		    'finding',
		    'moduleResolution',
		    'organization',
		    'package',
		    'packageFamily',
		    'pinoutValidation',
		    'project',
		    'revision',
		];
		function buildFrontendAuth(session, options) {
		    const accessibleOwnerIds = [
		        session.actor.userId,
		        ...session.memberships.filter((m) => m.role !== authentication_types_1.MembershipRole.Trainee).map((m) => m.groupId),
		    ];
		    const staff = (0, session_1.isStaff)(session);
		    const useStaffPermissions = staff && !options?.viewAsRole;
		    if (useStaffPermissions) {
		        return {
		            accessibleOwnerIds,
		            isStaff: true,
		            rules: [
		                ...exports.OWNED_ENTITY_SUBJECTS.map((subject) => ({
		                    action: 'manage',
		                    subject,
		                })),
		                {
		                    action: 'manage',
		                    subject: 'devTools',
		                },
		                {
		                    action: 'manage',
		                    subject: 'legacyFinding',
		                },
		                {
		                    action: 'manage',
		                    subject: 'tag',
		                },
		                {
		                    action: 'manage',
		                    subject: 'manufacturer',
		                },
		                {
		                    action: 'manage',
		                    subject: 'wizard',
		                },
		                {
		                    action: 'manage',
		                    subject: 'wizardAdmin',
		                },
		                {
		                    action: 'manage',
		                    subject: 'membership',
		                },
		            ],
		        };
		    }
		    const rules = [
		        {
		            action: 'view',
		            subject: 'analysisResult',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'view',
		            subject: 'file',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'manage',
		            subject: 'finding',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'view',
		            subject: 'moduleResolution',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'view',
		            subject: 'organization',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'view',
		            subject: 'package',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'manage',
		            subject: 'project',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		        {
		            action: 'manage',
		            subject: 'revision',
		            conditions: {
		                ownerId: {
		                    $in: accessibleOwnerIds,
		                },
		            },
		        },
		    ];
		    const adminIds = (0, session_1.adminOrgIds)(session);
		    if (adminIds.length > 0) {
		        rules.push({
		            action: 'manage',
		            subject: 'membership',
		            conditions: {
		                ownerId: {
		                    $in: adminIds,
		                },
		            },
		        }, {
		            action: 'manage',
		            subject: 'organization',
		            conditions: {
		                ownerId: {
		                    $in: adminIds,
		                },
		            },
		        });
		    }
		    if (options?.hasWizardAccess) {
		        rules.push({
		            action: 'manage',
		            subject: 'wizard',
		        });
		    }
		    if (options?.hasWizardAdminAccess) {
		        rules.push({
		            action: 'manage',
		            subject: 'wizardAdmin',
		        });
		    }
		    if (options?.hasPlanSelectorAccess) {
		        rules.push({
		            action: 'manage',
		            subject: 'planSelector',
		        });
		    }
		    return {
		        accessibleOwnerIds,
		        isStaff: staff,
		        rules,
		    };
		}
		
	} (frontendAuth));
	return frontendAuth;
}

var authentication_errors = {};

var hasRequiredAuthentication_errors;

function requireAuthentication_errors () {
	if (hasRequiredAuthentication_errors) return authentication_errors;
	hasRequiredAuthentication_errors = 1;
	Object.defineProperty(authentication_errors, "__esModule", { value: true });
	authentication_errors.UnauthorizedError = authentication_errors.AuthError = void 0;
	authentication_errors.isUnauthorizedError = isUnauthorizedError;
	class AuthError extends Error {
	    constructor(message) {
	        super(message);
	        this.name = this.constructor.name;
	    }
	}
	authentication_errors.AuthError = AuthError;
	class UnauthorizedError extends AuthError {
	    constructor(message = 'Authentication required') {
	        super(message);
	    }
	}
	authentication_errors.UnauthorizedError = UnauthorizedError;
	function isUnauthorizedError(error) {
	    return error instanceof UnauthorizedError;
	}
	
	return authentication_errors;
}

var authorization_errors = {};

var hasRequiredAuthorization_errors;

function requireAuthorization_errors () {
	if (hasRequiredAuthorization_errors) return authorization_errors;
	hasRequiredAuthorization_errors = 1;
	Object.defineProperty(authorization_errors, "__esModule", { value: true });
	authorization_errors.AuthorizationError = void 0;
	authorization_errors.isAuthorizationError = isAuthorizationError;
	const authentication_errors_1 = requireAuthentication_errors();
	class AuthorizationError extends authentication_errors_1.AuthError {
	    constructor(action, resource) {
	        super(`Cannot ${action} on ${resource.kind}:${resource.id}`);
	        this.action = action;
	        this.resource = resource;
	    }
	}
	authorization_errors.AuthorizationError = AuthorizationError;
	function isAuthorizationError(error) {
	    return error instanceof AuthorizationError;
	}
	
	return authorization_errors;
}

var domain_errors = {};

var hasRequiredDomain_errors;

function requireDomain_errors () {
	if (hasRequiredDomain_errors) return domain_errors;
	hasRequiredDomain_errors = 1;
	Object.defineProperty(domain_errors, "__esModule", { value: true });
	domain_errors.ValidationError = domain_errors.ResourceNotFoundError = void 0;
	domain_errors.isResourceNotFoundError = isResourceNotFoundError;
	domain_errors.isValidationError = isValidationError;
	class ResourceNotFoundError extends Error {
	    constructor(kind, id) {
	        super(`${kind} not found: ${id}`);
	        this.kind = kind;
	        this.id = id;
	        this.name = this.constructor.name;
	    }
	}
	domain_errors.ResourceNotFoundError = ResourceNotFoundError;
	class ValidationError extends Error {
	    constructor(message) {
	        super(message);
	        this.name = this.constructor.name;
	    }
	}
	domain_errors.ValidationError = ValidationError;
	function isResourceNotFoundError(error) {
	    return error instanceof ResourceNotFoundError;
	}
	function isValidationError(error) {
	    return error instanceof ValidationError;
	}
	
	return domain_errors;
}

var utils = {};

var hasRequiredUtils$1;

function requireUtils$1 () {
	if (hasRequiredUtils$1) return utils;
	hasRequiredUtils$1 = 1;
	Object.defineProperty(utils, "__esModule", { value: true });
	utils.ErrorExposureLevel = void 0;
	utils.getTokenExpiration = getTokenExpiration;
	utils.extractBearerToken = extractBearerToken;
	utils.getErrorExposureLevel = getErrorExposureLevel;
	const session_1 = requireSession();
	function getTokenExpiration(token) {
	    try {
	        const parts = token.split('.');
	        const payload = parts[1];
	        if (!payload) {
	            return null;
	        }
	        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
	        const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
	        const parsed = JSON.parse(decoded);
	        if (typeof parsed.exp === 'number' && Number.isFinite(parsed.exp) && parsed.exp > 0) {
	            return parsed.exp;
	        }
	        return null;
	    }
	    catch {
	        return null;
	    }
	}
	function extractBearerToken(authHeader) {
	    if (!authHeader) {
	        return null;
	    }
	    const parts = authHeader.split(' ');
	    const scheme = parts[0];
	    const token = parts[1];
	    if (parts.length !== 2 || !scheme || !token) {
	        return null;
	    }
	    if (scheme.toLowerCase() !== 'bearer') {
	        return null;
	    }
	    return token;
	}
	var ErrorExposureLevel;
	(function (ErrorExposureLevel) {
	    ErrorExposureLevel["Public"] = "public";
	    ErrorExposureLevel["Staff"] = "staff";
	    ErrorExposureLevel["StaffDetailed"] = "staff-detailed";
	})(ErrorExposureLevel || (utils.ErrorExposureLevel = ErrorExposureLevel = {}));
	function getErrorExposureLevel(session, allowDetailedErrors) {
	    if (!session || !(0, session_1.isStaff)(session)) {
	        return ErrorExposureLevel.Public;
	    }
	    if (allowDetailedErrors === true) {
	        return ErrorExposureLevel.StaffDetailed;
	    }
	    return ErrorExposureLevel.Staff;
	}
	
	return utils;
}

var hasRequiredAuth;

function requireAuth () {
	if (hasRequiredAuth) return Auth;
	hasRequiredAuth = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ErrorExposureLevel = exports.getErrorExposureLevel = exports.extractBearerToken = exports.getTokenExpiration = exports.isValidationError = exports.isResourceNotFoundError = exports.ValidationError = exports.ResourceNotFoundError = exports.isAuthorizationError = exports.AuthorizationError = exports.isUnauthorizedError = exports.UnauthorizedError = exports.AuthError = exports.OWNED_ENTITY_SUBJECTS = exports.buildFrontendAuth = exports.REVIEWER_ROLES_ASCENDING = exports.ReviewerRoles = exports.ReviewerRoleSchema = exports.ReviewerRole = exports.AUTH0_ORG_NAME_CLAIM = exports.AUTH0_ORG_ID_CLAIM = exports.AUTH0_CLAIM_NAMESPACE = exports.STAFF_ORG_USERNAME = exports.isReservedOrgUsername = exports.assertNoStaffEscalation = exports.adminOrgIds = exports.accessibleOwnerIds = exports.isStaff = exports.UNATTENDED_EDIT_PROVENANCE = exports.EditProvenanceSchema = exports.EditAgencySchema = exports.EditAgency = exports.ActorSchema = exports.MembershipSchema = exports.GroupTypeSchema = exports.ActorTypeSchema = exports.MembershipRoleSchema = exports.UserRecordTypeSchema = exports.GroupType = exports.ActorType = exports.MembershipRole = exports.UserRecordType = void 0;
		var authentication_types_1 = requireAuthentication_types();
		Object.defineProperty(exports, "UserRecordType", { enumerable: true, get: function () { return authentication_types_1.UserRecordType; } });
		Object.defineProperty(exports, "MembershipRole", { enumerable: true, get: function () { return authentication_types_1.MembershipRole; } });
		Object.defineProperty(exports, "ActorType", { enumerable: true, get: function () { return authentication_types_1.ActorType; } });
		Object.defineProperty(exports, "GroupType", { enumerable: true, get: function () { return authentication_types_1.GroupType; } });
		Object.defineProperty(exports, "UserRecordTypeSchema", { enumerable: true, get: function () { return authentication_types_1.UserRecordTypeSchema; } });
		Object.defineProperty(exports, "MembershipRoleSchema", { enumerable: true, get: function () { return authentication_types_1.MembershipRoleSchema; } });
		Object.defineProperty(exports, "ActorTypeSchema", { enumerable: true, get: function () { return authentication_types_1.ActorTypeSchema; } });
		Object.defineProperty(exports, "GroupTypeSchema", { enumerable: true, get: function () { return authentication_types_1.GroupTypeSchema; } });
		Object.defineProperty(exports, "MembershipSchema", { enumerable: true, get: function () { return authentication_types_1.MembershipSchema; } });
		Object.defineProperty(exports, "ActorSchema", { enumerable: true, get: function () { return authentication_types_1.ActorSchema; } });
		var edit_provenance_types_1 = requireEditProvenance_types();
		Object.defineProperty(exports, "EditAgency", { enumerable: true, get: function () { return edit_provenance_types_1.EditAgency; } });
		Object.defineProperty(exports, "EditAgencySchema", { enumerable: true, get: function () { return edit_provenance_types_1.EditAgencySchema; } });
		Object.defineProperty(exports, "EditProvenanceSchema", { enumerable: true, get: function () { return edit_provenance_types_1.EditProvenanceSchema; } });
		Object.defineProperty(exports, "UNATTENDED_EDIT_PROVENANCE", { enumerable: true, get: function () { return edit_provenance_types_1.UNATTENDED_EDIT_PROVENANCE; } });
		var session_1 = requireSession();
		Object.defineProperty(exports, "isStaff", { enumerable: true, get: function () { return session_1.isStaff; } });
		Object.defineProperty(exports, "accessibleOwnerIds", { enumerable: true, get: function () { return session_1.accessibleOwnerIds; } });
		Object.defineProperty(exports, "adminOrgIds", { enumerable: true, get: function () { return session_1.adminOrgIds; } });
		Object.defineProperty(exports, "assertNoStaffEscalation", { enumerable: true, get: function () { return session_1.assertNoStaffEscalation; } });
		Object.defineProperty(exports, "isReservedOrgUsername", { enumerable: true, get: function () { return session_1.isReservedOrgUsername; } });
		Object.defineProperty(exports, "STAFF_ORG_USERNAME", { enumerable: true, get: function () { return session_1.STAFF_ORG_USERNAME; } });
		Object.defineProperty(exports, "AUTH0_CLAIM_NAMESPACE", { enumerable: true, get: function () { return session_1.AUTH0_CLAIM_NAMESPACE; } });
		Object.defineProperty(exports, "AUTH0_ORG_ID_CLAIM", { enumerable: true, get: function () { return session_1.AUTH0_ORG_ID_CLAIM; } });
		Object.defineProperty(exports, "AUTH0_ORG_NAME_CLAIM", { enumerable: true, get: function () { return session_1.AUTH0_ORG_NAME_CLAIM; } });
		var reviewer_role_1 = requireReviewerRole();
		Object.defineProperty(exports, "ReviewerRole", { enumerable: true, get: function () { return reviewer_role_1.ReviewerRole; } });
		Object.defineProperty(exports, "ReviewerRoleSchema", { enumerable: true, get: function () { return reviewer_role_1.ReviewerRoleSchema; } });
		Object.defineProperty(exports, "ReviewerRoles", { enumerable: true, get: function () { return reviewer_role_1.ReviewerRoles; } });
		Object.defineProperty(exports, "REVIEWER_ROLES_ASCENDING", { enumerable: true, get: function () { return reviewer_role_1.REVIEWER_ROLES_ASCENDING; } });
		var frontend_auth_1 = requireFrontendAuth();
		Object.defineProperty(exports, "buildFrontendAuth", { enumerable: true, get: function () { return frontend_auth_1.buildFrontendAuth; } });
		Object.defineProperty(exports, "OWNED_ENTITY_SUBJECTS", { enumerable: true, get: function () { return frontend_auth_1.OWNED_ENTITY_SUBJECTS; } });
		var authentication_errors_1 = requireAuthentication_errors();
		Object.defineProperty(exports, "AuthError", { enumerable: true, get: function () { return authentication_errors_1.AuthError; } });
		Object.defineProperty(exports, "UnauthorizedError", { enumerable: true, get: function () { return authentication_errors_1.UnauthorizedError; } });
		Object.defineProperty(exports, "isUnauthorizedError", { enumerable: true, get: function () { return authentication_errors_1.isUnauthorizedError; } });
		var authorization_errors_1 = requireAuthorization_errors();
		Object.defineProperty(exports, "AuthorizationError", { enumerable: true, get: function () { return authorization_errors_1.AuthorizationError; } });
		Object.defineProperty(exports, "isAuthorizationError", { enumerable: true, get: function () { return authorization_errors_1.isAuthorizationError; } });
		var domain_errors_1 = requireDomain_errors();
		Object.defineProperty(exports, "ResourceNotFoundError", { enumerable: true, get: function () { return domain_errors_1.ResourceNotFoundError; } });
		Object.defineProperty(exports, "ValidationError", { enumerable: true, get: function () { return domain_errors_1.ValidationError; } });
		Object.defineProperty(exports, "isResourceNotFoundError", { enumerable: true, get: function () { return domain_errors_1.isResourceNotFoundError; } });
		Object.defineProperty(exports, "isValidationError", { enumerable: true, get: function () { return domain_errors_1.isValidationError; } });
		var utils_1 = requireUtils$1();
		Object.defineProperty(exports, "getTokenExpiration", { enumerable: true, get: function () { return utils_1.getTokenExpiration; } });
		Object.defineProperty(exports, "extractBearerToken", { enumerable: true, get: function () { return utils_1.extractBearerToken; } });
		Object.defineProperty(exports, "getErrorExposureLevel", { enumerable: true, get: function () { return utils_1.getErrorExposureLevel; } });
		Object.defineProperty(exports, "ErrorExposureLevel", { enumerable: true, get: function () { return utils_1.ErrorExposureLevel; } });
		
	} (Auth));
	return Auth;
}

var Billing = {};

var aiFeature = {};

var hasRequiredAiFeature;

function requireAiFeature () {
	if (hasRequiredAiFeature) return aiFeature;
	hasRequiredAiFeature = 1;
	Object.defineProperty(aiFeature, "__esModule", { value: true });
	aiFeature.AiFeature = void 0;
	var AiFeature;
	(function (AiFeature) {
	    AiFeature["AiChat"] = "ai_chat";
	    AiFeature["CustomValidation"] = "custom_validation";
	})(AiFeature || (aiFeature.AiFeature = AiFeature = {}));
	
	return aiFeature;
}

var billingClass = {};

var hasRequiredBillingClass;

function requireBillingClass () {
	if (hasRequiredBillingClass) return billingClass;
	hasRequiredBillingClass = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BillingClassSchema = exports.BILLING_CLASSES = void 0;
		exports.isBillableClass = isBillableClass;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.BILLING_CLASSES = ['standard', 'internal', 'pilot', 'design_partner'];
		exports.BillingClassSchema = zod_1.z.enum(exports.BILLING_CLASSES);
		function isBillableClass(billingClass) {
		    return billingClass === 'standard';
		}
		
	} (billingClass));
	return billingClass;
}

var tokenCreditFormula = {};

var hasRequiredTokenCreditFormula;

function requireTokenCreditFormula () {
	if (hasRequiredTokenCreditFormula) return tokenCreditFormula;
	hasRequiredTokenCreditFormula = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.MODEL_TOKEN_CREDIT_RATES = void 0;
		exports.creditsForTokenUsage = creditsForTokenUsage;
		exports.MODEL_TOKEN_CREDIT_RATES = {
		    'claude-opus-4-8': {
		        inputPerMillion: 3,
		        outputPerMillion: 15,
		        cacheReadPerMillion: 0.3,
		        cacheWritePerMillion: 3.75,
		    },
		};
		function creditsForTokenUsage(tokens, model) {
		    const rates = exports.MODEL_TOKEN_CREDIT_RATES[model];
		    if (rates === undefined) {
		        throw new Error(`No token credit rate is defined for model '${model}'`);
		    }
		    const uncachedInput = Math.max(0, tokens.inputTokens - tokens.cacheReadTokens - tokens.cacheWriteTokens);
		    return ((uncachedInput * rates.inputPerMillion +
		        tokens.cacheReadTokens * rates.cacheReadPerMillion +
		        tokens.cacheWriteTokens * rates.cacheWritePerMillion +
		        tokens.outputTokens * rates.outputPerMillion) /
		        1_000_000);
		}
		
	} (tokenCreditFormula));
	return tokenCreditFormula;
}

var hasRequiredBilling;

function requireBilling () {
	if (hasRequiredBilling) return Billing;
	hasRequiredBilling = 1;
	(function (exports) {
		var __createBinding = (Billing && Billing.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Billing && Billing.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAiFeature(), exports);
		__exportStar(requireBillingClass(), exports);
		__exportStar(requireTokenCreditFormula(), exports);
		
	} (Billing));
	return Billing;
}

var BlockDiagram = {};

var blockDiagramDocument = {};

var hasRequiredBlockDiagramDocument;

function requireBlockDiagramDocument () {
	if (hasRequiredBlockDiagramDocument) return blockDiagramDocument;
	hasRequiredBlockDiagramDocument = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BlockDiagramDocumentSchema = exports.BlockDiagramGenerationSchema = exports.BlockDiagramPlacementSchema = exports.BlockDiagramEdgeSchema = exports.BlockDiagramBlockSchema = exports.BlockDiagramVariantSchema = exports.EdgeKindSchema = exports.BlockKindSchema = exports.BLOCK_DIAGRAM_ATTACHMENT_TYPE = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.BLOCK_DIAGRAM_ATTACHMENT_TYPE = 'block-diagram';
		exports.BlockKindSchema = zod_1.z.enum(['processor', 'power', 'connector', 'other']);
		exports.EdgeKindSchema = zod_1.z.enum(['rail', 'bus', 'control', 'analog']);
		exports.BlockDiagramVariantSchema = zod_1.z.enum(['full', 'power', 'signal']);
		exports.BlockDiagramBlockSchema = zod_1.z.object({
		    id: zod_1.z.string().min(1),
		    name: zod_1.z.string(),
		    sub: zod_1.z.string().optional(),
		    kind: exports.BlockKindSchema,
		    refdes: zod_1.z.string().array().optional(),
		    sheets: zod_1.z.string().array().optional(),
		});
		exports.BlockDiagramEdgeSchema = zod_1.z.object({
		    from: zod_1.z.string().min(1),
		    to: zod_1.z.string().min(1),
		    kind: exports.EdgeKindSchema,
		    label: zod_1.z.string().optional(),
		    nets: zod_1.z.string().array().optional(),
		    directed: zod_1.z.boolean().optional(),
		    render: zod_1.z.boolean().optional(),
		});
		exports.BlockDiagramPlacementSchema = zod_1.z.object({
		    hub: zod_1.z.string().optional(),
		    cells: zod_1.z.record(zod_1.z.string(), zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()])),
		    spans: zod_1.z
		        .record(zod_1.z.string(), zod_1.z.object({
		        w: zod_1.z.number().positive().optional(),
		        h: zod_1.z.number().positive().optional(),
		    }))
		        .optional(),
		    spacing: zod_1.z.number().positive().optional(),
		});
		exports.BlockDiagramGenerationSchema = zod_1.z.object({
		    generatedAt: zod_1.z.string(),
		    projectId: zod_1.z.string(),
		    buildId: zod_1.z.string(),
		    rung: zod_1.z.number().int().min(1).max(3).optional(),
		    skillVersion: zod_1.z.string().optional(),
		    layoutEngineVersion: zod_1.z.string().optional(),
		    generator: zod_1.z.string().optional(),
		});
		exports.BlockDiagramDocumentSchema = zod_1.z.object({
		    schemaVersion: zod_1.z.literal(1),
		    title: zod_1.z.string(),
		    blocks: exports.BlockDiagramBlockSchema.array(),
		    edges: exports.BlockDiagramEdgeSchema.array(),
		    placement: exports.BlockDiagramPlacementSchema.optional(),
		    generation: exports.BlockDiagramGenerationSchema,
		});
		
	} (blockDiagramDocument));
	return blockDiagramDocument;
}

var collapsedEdge = {};

var hasRequiredCollapsedEdge;

function requireCollapsedEdge () {
	if (hasRequiredCollapsedEdge) return collapsedEdge;
	hasRequiredCollapsedEdge = 1;
	Object.defineProperty(collapsedEdge, "__esModule", { value: true });
	
	return collapsedEdge;
}

var deriveVariant = {};

var hasRequiredDeriveVariant;

function requireDeriveVariant () {
	if (hasRequiredDeriveVariant) return deriveVariant;
	hasRequiredDeriveVariant = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.POWER_SEQUENCE_LABEL_RE = void 0;
		exports.collapseEdges = collapseEdges;
		exports.deriveVariant = deriveVariant;
		const KIND_RANK = { rail: 3, bus: 2, control: 1, analog: 1 };
		exports.POWER_SEQUENCE_LABEL_RE = /PG|power.?good|seq|\bEN\b|enable|CHRG|BOOST|CAM_ON|rst|reset/i;
		const MERGED_LABEL_CAP = 52;
		const EDGE_LABEL_CAP = 26;
		function sanitize(s) {
		    return s.replace(/"/g, "'").replace(/&/g, '+').replace(/</g, '(').replace(/>/g, ')');
		}
		function mergeLabels(labels) {
		    const seen = new Set();
		    const out = [];
		    for (const raw of labels) {
		        const l = sanitize(raw);
		        if (l && !seen.has(l)) {
		            seen.add(l);
		            out.push(l);
		        }
		    }
		    let s = out.join(' · ');
		    if (s.length > MERGED_LABEL_CAP) {
		        const kept = [];
		        let total = 0;
		        for (const l of out) {
		            if (total + l.length > MERGED_LABEL_CAP && kept.length > 0) {
		                kept.push(`+${out.length - kept.length} more`);
		                break;
		            }
		            kept.push(l);
		            total += l.length;
		        }
		        s = kept.join(' · ');
		    }
		    return s;
		}
		function collapseEdges(edges) {
		    const pairs = new Map();
		    for (const e of edges) {
		        const [a, b] = e.from <= e.to ? [e.from, e.to] : [e.to, e.from];
		        const key = `${a} ${b}`;
		        let entry = pairs.get(key);
		        if (!entry) {
		            entry = { a, b, acc: { labels: [], dirs: new Map(), nets: new Set() } };
		            pairs.set(key, entry);
		        }
		        const kind = e.kind;
		        entry.acc.labels.push({ rank: KIND_RANK[kind] ?? 0, label: e.label ?? '' });
		        let d = entry.acc.dirs.get(kind);
		        if (!d) {
		            d = [false, false];
		            entry.acc.dirs.set(kind, d);
		        }
		        if (e.directed ?? true) {
		            d[e.from === a ? 0 : 1] = true;
		        }
		        for (const net of e.nets ?? []) {
		            entry.acc.nets.add(net);
		        }
		    }
		    const out = [];
		    for (const { a, b, acc } of pairs.values()) {
		        let kind = 'bus';
		        let bestRank = -1;
		        for (const k of acc.dirs.keys()) {
		            const rank = KIND_RANK[k] ?? 0;
		            if (rank > bestRank) {
		                bestRank = rank;
		                kind = k;
		            }
		        }
		        const [fwd, back] = acc.dirs.get(kind) ?? [false, false];
		        const labels = [...acc.labels].sort((x, y) => y.rank - x.rank).map((t) => t.label);
		        out.push({ a, b, kind, label: mergeLabels(labels), fwd, back, nets: [...acc.nets] });
		    }
		    return out;
		}
		function trimLabel(edge) {
		    if (edge.label.length > EDGE_LABEL_CAP) {
		        return { ...edge, label: edge.label.slice(0, EDGE_LABEL_CAP - 2) + '…' };
		    }
		    return edge;
		}
		function deriveVariant(doc, variant) {
		    const kindById = new Map(doc.blocks.map((b) => [b.id, b.kind]));
		    const rendered = doc.edges.filter((e) => e.render !== false);
		    let filtered;
		    switch (variant) {
		        case 'full':
		            filtered = rendered;
		            break;
		        case 'power':
		            filtered = rendered.filter((e) => e.kind === 'rail' ||
		                ((e.kind === 'control' || e.kind === 'analog') &&
		                    exports.POWER_SEQUENCE_LABEL_RE.test(e.label ?? '') &&
		                    (kindById.get(e.from) === 'power' || kindById.get(e.to) === 'power')));
		            break;
		        case 'signal':
		            filtered = rendered.filter((e) => e.kind !== 'rail');
		            break;
		    }
		    return collapseEdges(filtered).map(trimLabel);
		}
		
	} (deriveVariant));
	return deriveVariant;
}

var voltage = {};

var hasRequiredVoltage;

function requireVoltage () {
	if (hasRequiredVoltage) return voltage;
	hasRequiredVoltage = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.VOLTAGE_RANK_COUNT = void 0;
		exports.parseVoltage = parseVoltage;
		exports.computeVoltageScale = computeVoltageScale;
		exports.voltageRankForLabel = voltageRankForLabel;
		exports.VOLTAGE_RANK_COUNT = 7;
		function parseVoltage(label) {
		    let max = null;
		    for (const m of label.matchAll(/(\d+(?:\.\d+)?)\s*V\b/gi)) {
		        const v = parseFloat(m[1]);
		        if (max === null || v > max) {
		            max = v;
		        }
		    }
		    for (const m of label.matchAll(/\b[PN]?(\d+)V(\d+)\b/g)) {
		        const v = parseFloat(`${m[1]}.${m[2]}`);
		        if (max === null || v > max) {
		            max = v;
		        }
		    }
		    return max;
		}
		function computeVoltageScale(fullVariantEdges) {
		    const volts = new Set();
		    for (const e of fullVariantEdges) {
		        if (e.kind !== 'rail') {
		            continue;
		        }
		        const v = parseVoltage(e.label);
		        if (v !== null) {
		            volts.add(v);
		        }
		    }
		    const sorted = [...volts].sort((a, b) => b - a);
		    const n = sorted.length;
		    return sorted.map((voltage, i) => ({
		        voltage,
		        rank: n === 1 ? 0 : Math.round((i * (exports.VOLTAGE_RANK_COUNT - 1)) / (n - 1)),
		        label: `${voltage}V`,
		    }));
		}
		function voltageRankForLabel(scale, label) {
		    const v = parseVoltage(label);
		    if (v === null) {
		        return null;
		    }
		    const entry = scale.find((s) => s.voltage === v);
		    return entry ? entry.rank : null;
		}
		
	} (voltage));
	return voltage;
}

var layoutTypes = {};

var hasRequiredLayoutTypes;

function requireLayoutTypes () {
	if (hasRequiredLayoutTypes) return layoutTypes;
	hasRequiredLayoutTypes = 1;
	Object.defineProperty(layoutTypes, "__esModule", { value: true });
	
	return layoutTypes;
}

var assignColumns = {};

var hasRequiredAssignColumns;

function requireAssignColumns () {
	if (hasRequiredAssignColumns) return assignColumns;
	hasRequiredAssignColumns = 1;
	Object.defineProperty(assignColumns, "__esModule", { value: true });
	assignColumns.assignColumns = assignColumns$1;
	function assignColumns$1(blocks, edges) {
	    const deg = new Map();
	    const outDeg = new Map();
	    const inDeg = new Map();
	    for (const e of edges) {
	        deg.set(e.a, (deg.get(e.a) ?? 0) + 1);
	        deg.set(e.b, (deg.get(e.b) ?? 0) + 1);
	        if (e.fwd && !e.back) {
	            outDeg.set(e.a, (outDeg.get(e.a) ?? 0) + 1);
	            inDeg.set(e.b, (inDeg.get(e.b) ?? 0) + 1);
	        }
	        if (e.back && !e.fwd) {
	            outDeg.set(e.b, (outDeg.get(e.b) ?? 0) + 1);
	            inDeg.set(e.a, (inDeg.get(e.a) ?? 0) + 1);
	        }
	    }
	    const used = blocks.filter((b) => deg.has(b.id));
	    const hub = [...used].filter((b) => b.kind !== 'power').sort((x, y) => (deg.get(y.id) ?? 0) - (deg.get(x.id) ?? 0))[0] ?? null;
	    const powerIds = new Set(used.filter((b) => b.kind === 'power').map((b) => b.id));
	    const railAdj = new Map();
	    for (const e of edges) {
	        if (e.kind !== 'rail') {
	            continue;
	        }
	        const s = e.fwd && !e.back ? e.a
	            : e.back && !e.fwd ? e.b
	                : null;
	        const t = e.fwd && !e.back ? e.b
	            : e.back && !e.fwd ? e.a
	                : null;
	        if (s !== null && t !== null && powerIds.has(s) && powerIds.has(t)) {
	            const list = railAdj.get(s) ?? [];
	            list.push(t);
	            railAdj.set(s, list);
	        }
	    }
	    const down = new Map();
	    const dfs = (id, stack) => {
	        const memo = down.get(id);
	        if (memo !== undefined) {
	            return memo;
	        }
	        if (stack.has(id)) {
	            return 0;
	        }
	        stack.add(id);
	        const r = 1 + Math.max(-1, ...(railAdj.get(id) ?? []).map((t) => dfs(t, stack)));
	        stack.delete(id);
	        down.set(id, r);
	        return r;
	    };
	    for (const id of powerIds) {
	        dfs(id, new Set());
	    }
	    const maxDown = Math.max(0, ...[...powerIds].map((i) => down.get(i) ?? 0));
	    const depth = new Map();
	    for (const id of powerIds) {
	        depth.set(id, maxDown - (down.get(id) ?? 0));
	    }
	    const powerCols = powerIds.size > 0 ? Math.max(...[...powerIds].map((i) => depth.get(i) ?? 0)) + 1 : 0;
	    const col = new Map();
	    for (const b of used) {
	        if (hub !== null && b.id === hub.id) {
	            col.set(b.id, 1 + powerCols);
	        }
	        else if (b.kind === 'power') {
	            col.set(b.id, 1 + (depth.get(b.id) ?? 0));
	        }
	        else if (b.kind === 'connector') {
	            col.set(b.id, (outDeg.get(b.id) ?? 0) > (inDeg.get(b.id) ?? 0) ? 0 : powerCols + 3);
	        }
	        else if (b.kind === 'processor') {
	            col.set(b.id, 1 + powerCols);
	        }
	        else {
	            col.set(b.id, 2 + powerCols);
	        }
	    }
	    const usedCols = [...new Set(col.values())].sort((a, b) => a - b);
	    const remap = new Map(usedCols.map((c, i) => [c, i]));
	    for (const [id, c] of col) {
	        col.set(id, remap.get(c) ?? 0);
	    }
	    return { used, col, nCols: usedCols.length, hub: hub === null ? null : hub.id };
	}
	
	return assignColumns;
}

var orderRows = {};

var hasRequiredOrderRows;

function requireOrderRows () {
	if (hasRequiredOrderRows) return orderRows;
	hasRequiredOrderRows = 1;
	Object.defineProperty(orderRows, "__esModule", { value: true });
	orderRows.orderRows = orderRows$1;
	function orderRows$1(used, col, edges, nCols) {
	    const cols = Array.from({ length: nCols }, () => []);
	    for (const b of used) {
	        cols[col.get(b.id) ?? 0].push(b.id);
	    }
	    const maxRows = Math.max(...cols.map((c) => c.length));
	    const slot = new Map();
	    const recenter = (c) => {
	        const start = Math.floor((maxRows - c.length) / 2);
	        c.forEach((id, i) => slot.set(id, start + i));
	    };
	    cols.forEach(recenter);
	    const nbrs = new Map();
	    for (const e of edges) {
	        const la = nbrs.get(e.a) ?? [];
	        la.push(e.b);
	        nbrs.set(e.a, la);
	        const lb = nbrs.get(e.b) ?? [];
	        lb.push(e.a);
	        nbrs.set(e.b, lb);
	    }
	    for (let it = 0; it < 10; it++) {
	        for (const c of it % 2 === 1 ? [...cols].reverse() : cols) {
	            const bary = (id) => {
	                const ns = (nbrs.get(id) ?? []).filter((n) => slot.has(n));
	                return ns.length > 0 ? ns.reduce((s, n) => s + (slot.get(n) ?? 0), 0) / ns.length : (slot.get(id) ?? 0);
	            };
	            c.sort((x, y) => bary(x) - bary(y) || (slot.get(x) ?? 0) - (slot.get(y) ?? 0));
	            recenter(c);
	        }
	    }
	    return { slot, maxRows };
	}
	
	return orderRows;
}

var allocLanes = {};

var hasRequiredAllocLanes;

function requireAllocLanes () {
	if (hasRequiredAllocLanes) return allocLanes;
	hasRequiredAllocLanes = 1;
	Object.defineProperty(allocLanes, "__esModule", { value: true });
	allocLanes.allocLanes = allocLanes$1;
	function allocLanes$1(items, pad) {
	    const groups = new Map();
	    const lanes = [];
	    const out = new Map();
	    for (const it of [...items].sort((a, b) => a.lo - b.lo)) {
	        if (it.group !== null && groups.has(it.group)) {
	            const l = groups.get(it.group) ?? 0;
	            out.set(it, l);
	            lanes[l].push([it.lo, it.hi]);
	            continue;
	        }
	        let l = 0;
	        for (; l < lanes.length; l++) {
	            if (lanes[l].every(([lo, hi]) => it.hi < lo - pad || it.lo > hi + pad)) {
	                break;
	            }
	        }
	        lanes[l] = lanes[l] ?? [];
	        lanes[l].push([it.lo, it.hi]);
	        out.set(it, l);
	        if (it.group !== null) {
	            groups.set(it.group, l);
	        }
	    }
	    return { out, n: lanes.length };
	}
	
	return allocLanes;
}

var computeLayout = {};

var constants$1 = {};

var hasRequiredConstants$1;

function requireConstants$1 () {
	if (hasRequiredConstants$1) return constants$1;
	hasRequiredConstants$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NODE_MAX_W_FACTOR = exports.BLOCK_LABEL_PAD_X = exports.TEXT_CHAR_W_PER_SIZE = exports.BOARD_LABEL_FONT_SIZE = exports.CHIP_FONT_SIZE = exports.SUB_FONT_SIZE_BIG = exports.SUB_FONT_SIZE = exports.NAME_FONT_SIZE_BIG = exports.NAME_FONT_SIZE = exports.EM_TO_CAP = exports.BOARD_OUTLINE_STROKE_W = exports.PROCESSOR_STROKE_W = exports.BLOCK_STROKE_W = exports.ARROW_HALF_W = exports.ARROW_LEN = exports.EDGE_STYLE = exports.CHIP_DEOVERLAP_DY = exports.CHIP_H = exports.CHIP_PAD_W = exports.CHIP_CHAR_W = exports.CORRIDOR_LANE_PAD = exports.GUTTER_LANE_PAD = exports.CORRIDOR_LANE_STEP = exports.GUTTER_EXTRA = exports.GUTTER_MIN = exports.BOARD_PAD = exports.MARGIN = exports.LANE_STEP = exports.GAP_Y = exports.NODE_H = exports.NODE_W = void 0;
		exports.estimateTextWidth = estimateTextWidth;
		exports.truncateTextToWidth = truncateTextToWidth;
		exports.NODE_W = 180;
		exports.NODE_H = 56;
		exports.GAP_Y = 48;
		exports.LANE_STEP = 13;
		exports.MARGIN = 40;
		exports.BOARD_PAD = 24;
		exports.GUTTER_MIN = 120;
		exports.GUTTER_EXTRA = 80;
		exports.CORRIDOR_LANE_STEP = 12;
		exports.GUTTER_LANE_PAD = 8;
		exports.CORRIDOR_LANE_PAD = 10;
		exports.CHIP_CHAR_W = 5.6;
		exports.CHIP_PAD_W = 10;
		exports.CHIP_H = 17;
		exports.CHIP_DEOVERLAP_DY = 18;
		exports.EDGE_STYLE = {
		    rail: { width: 3, lineStyle: 'solid' },
		    bus: { width: 2, lineStyle: 'solid' },
		    control: { width: 1.5, lineStyle: 'dash' },
		    analog: { width: 1.5, lineStyle: 'dot' },
		};
		exports.ARROW_LEN = 9;
		exports.ARROW_HALF_W = 4;
		exports.BLOCK_STROKE_W = 1.3;
		exports.PROCESSOR_STROKE_W = 2.5;
		exports.BOARD_OUTLINE_STROKE_W = 1.5;
		exports.EM_TO_CAP = 0.7;
		exports.NAME_FONT_SIZE = 12 * exports.EM_TO_CAP;
		exports.NAME_FONT_SIZE_BIG = 14.5 * exports.EM_TO_CAP;
		exports.SUB_FONT_SIZE = 9.5 * exports.EM_TO_CAP;
		exports.SUB_FONT_SIZE_BIG = 10.5 * exports.EM_TO_CAP;
		exports.CHIP_FONT_SIZE = 9.5 * exports.EM_TO_CAP;
		exports.BOARD_LABEL_FONT_SIZE = 10 * exports.EM_TO_CAP;
		exports.TEXT_CHAR_W_PER_SIZE = 0.6 / exports.EM_TO_CAP;
		exports.BLOCK_LABEL_PAD_X = 10;
		exports.NODE_MAX_W_FACTOR = 1.5;
		function estimateTextWidth(value, size) {
		    return value.length * size * exports.TEXT_CHAR_W_PER_SIZE;
		}
		function truncateTextToWidth(value, size, maxWidth) {
		    if (estimateTextWidth(value, size) <= maxWidth) {
		        return value;
		    }
		    const maxChars = Math.floor(maxWidth / (size * exports.TEXT_CHAR_W_PER_SIZE));
		    return value.slice(0, Math.max(maxChars - 1, 1)) + '…';
		}
		
	} (constants$1));
	return constants$1;
}

var hasRequiredComputeLayout;

function requireComputeLayout () {
	if (hasRequiredComputeLayout) return computeLayout;
	hasRequiredComputeLayout = 1;
	Object.defineProperty(computeLayout, "__esModule", { value: true });
	computeLayout.computeLayout = computeLayout$1;
	const alloc_lanes_1 = requireAllocLanes();
	const assign_columns_1 = requireAssignColumns();
	const order_rows_1 = requireOrderRows();
	const voltage_1 = requireVoltage();
	const constants_1 = requireConstants$1();
	function computeLayout$1(blocks, edges, placement, spacing, voltageScale) {
	    const warnings = [];
	    const kindOf = new Map(blocks.map((b) => [b.id, b.kind]));
	    let used;
	    const col = new Map();
	    const slot = new Map();
	    let nCols;
	    let maxRows;
	    let hub;
	    const hRows = new Map();
	    const wFac = new Map();
	    if (placement !== undefined && placement.cells !== undefined) {
	        const spans = placement.spans ?? {};
	        const connected = new Set();
	        for (const e of edges) {
	            connected.add(e.a);
	            connected.add(e.b);
	        }
	        used = blocks.filter((b) => connected.has(b.id) && placement.cells[b.id] !== undefined);
	        const dropped = blocks.filter((b) => connected.has(b.id) && placement.cells[b.id] === undefined);
	        for (const b of dropped) {
	            warnings.push(`Block '${b.id}' is connected but has no placement cell; dropped from the layout`);
	        }
	        const taken = new Set();
	        const rowSet = new Set();
	        const colSet = new Set();
	        for (const b of used) {
	            const [c, r0] = placement.cells[b.id];
	            let r = r0;
	            const h = Math.max(1, Math.round(spans[b.id]?.h ?? 1));
	            const w = Math.max(1, spans[b.id]?.w ?? 1);
	            const clash = (rr) => {
	                for (let k = 0; k < h; k++) {
	                    if (taken.has(`${c},${rr + k}`)) {
	                        return true;
	                    }
	                }
	                return false;
	            };
	            while (clash(r)) {
	                r += 1;
	            }
	            for (let k = 0; k < h; k++) {
	                taken.add(`${c},${r + k}`);
	                rowSet.add(r + k);
	            }
	            colSet.add(c);
	            col.set(b.id, c);
	            slot.set(b.id, r);
	            hRows.set(b.id, h);
	            wFac.set(b.id, w);
	        }
	        const cVals = [...colSet].sort((a, b) => a - b);
	        const rVals = [...rowSet].sort((a, b) => a - b);
	        const cMap = new Map(cVals.map((v, i) => [v, i]));
	        const rMap = new Map(rVals.map((v, i) => [v, i]));
	        maxRows = 0;
	        for (const id of col.keys()) {
	            col.set(id, cMap.get(col.get(id) ?? 0) ?? 0);
	            slot.set(id, rMap.get(slot.get(id) ?? 0) ?? 0);
	            maxRows = Math.max(maxRows, (slot.get(id) ?? 0) + (hRows.get(id) ?? 1));
	        }
	        nCols = cVals.length;
	        hub = placement.hub ?? null;
	    }
	    else {
	        const assignment = (0, assign_columns_1.assignColumns)(blocks, edges);
	        used = assignment.used;
	        for (const [id, c] of assignment.col) {
	            col.set(id, c);
	        }
	        nCols = assignment.nCols;
	        hub = assignment.hub;
	        const ordering = (0, order_rows_1.orderRows)(used, col, edges, nCols);
	        for (const [id, r] of ordering.slot) {
	            slot.set(id, r);
	        }
	        maxRows = ordering.maxRows;
	        for (const b of used) {
	            hRows.set(b.id, 1);
	            wFac.set(b.id, 1);
	        }
	    }
	    if (used.length === 0 || nCols === 0) {
	        return {
	            nodes: [],
	            edges: [],
	            chips: [],
	            boardOutline: { x: constants_1.MARGIN, y: constants_1.MARGIN, w: 2 * constants_1.BOARD_PAD, h: 2 * constants_1.BOARD_PAD },
	            canvas: { width: 2 * (constants_1.MARGIN + constants_1.BOARD_PAD), height: 2 * (constants_1.MARGIN + constants_1.BOARD_PAD) },
	            warnings: [...warnings, 'No connected, placed blocks to lay out'],
	        };
	    }
	    const S = spacing;
	    const gapY = Math.round(constants_1.GAP_Y * S);
	    const pitch = constants_1.NODE_H + gapY;
	    const gutterMin = Math.round(constants_1.GUTTER_MIN * S);
	    const laneStep = Math.round(constants_1.LANE_STEP * S);
	    const nodeH = (id) => constants_1.NODE_H * (hRows.get(id) ?? 1) + gapY * ((hRows.get(id) ?? 1) - 1);
	    const labelW = new Map();
	    for (const b of used) {
	        const big = (hRows.get(b.id) ?? 1) > 1 || (wFac.get(b.id) ?? 1) > 1;
	        const nameW = (0, constants_1.estimateTextWidth)(b.name, big ? constants_1.NAME_FONT_SIZE_BIG : constants_1.NAME_FONT_SIZE);
	        const subW = b.sub !== undefined ? (0, constants_1.estimateTextWidth)(b.sub, big ? constants_1.SUB_FONT_SIZE_BIG : constants_1.SUB_FONT_SIZE) : 0;
	        labelW.set(b.id, Math.max(nameW, subW) + 2 * constants_1.BLOCK_LABEL_PAD_X);
	    }
	    const nodeW = (id) => Math.max(Math.round(constants_1.NODE_W * (wFac.get(id) ?? 1)), Math.ceil(Math.min(labelW.get(id) ?? 0, constants_1.NODE_W * constants_1.NODE_MAX_W_FACTOR)));
	    const topY = constants_1.MARGIN + constants_1.BOARD_PAD + 14;
	    const rowY = (r) => topY + r * pitch;
	    const corrY = (k) => topY + k * pitch - gapY / 2;
	    const colBlocks = Array.from({ length: nCols }, () => []);
	    for (const b of used) {
	        colBlocks[col.get(b.id) ?? 0].push(b.id);
	    }
	    const corridorClear = (k, cLo, cHi) => {
	        for (let c = cLo; c <= cHi; c++) {
	            for (const id of colBlocks[c]) {
	                const s0 = slot.get(id) ?? 0;
	                if (s0 < k && k <= s0 + (hRows.get(id) ?? 1) - 1) {
	                    return false;
	                }
	            }
	        }
	        return true;
	    };
	    const plans = [];
	    for (const e of edges) {
	        let s = e.a;
	        let t = e.b;
	        let dir = 'both';
	        if (e.fwd && !e.back) {
	            dir = 'fwd';
	        }
	        else if (e.back && !e.fwd) {
	            s = e.b;
	            t = e.a;
	            dir = 'fwd';
	        }
	        const cs = col.get(s);
	        const ct = col.get(t);
	        if (cs === undefined || ct === undefined) {
	            continue;
	        }
	        plans.push({ e, s, t, dir, cs, ct, key: '' });
	    }
	    let eIdx = 0;
	    for (const p of plans) {
	        p.key = p.e.kind === 'rail' ? `r|${p.s}|${p.e.label}` : `e${eIdx++}`;
	        if (p.e.kind === 'rail') {
	            p.voltageRank = (0, voltage_1.voltageRankForLabel)(voltageScale, p.e.label);
	        }
	    }
	    const portReq = new Map();
	    const req = (id, side, farSlot, plan, which) => {
	        const k = `${id}|${side}`;
	        let rec = portReq.get(k);
	        if (!rec) {
	            rec = { id, reqs: [] };
	            portReq.set(k, rec);
	        }
	        rec.reqs.push({ farSlot, plan, which });
	    };
	    for (const p of plans) {
	        const sSlot = slot.get(p.s) ?? 0;
	        const tSlot = slot.get(p.t) ?? 0;
	        if (p.cs === p.ct && (tSlot === sSlot + (hRows.get(p.s) ?? 1) || sSlot === tSlot + (hRows.get(p.t) ?? 1))) {
	            p.route = 'V';
	            continue;
	        }
	        if (p.cs < p.ct) {
	            req(p.s, 'R', tSlot, p, 'exit');
	            req(p.t, 'L', sSlot, p, 'entry');
	        }
	        else if (p.cs > p.ct) {
	            req(p.s, 'L', tSlot, p, 'exit');
	            req(p.t, 'R', sSlot, p, 'entry');
	        }
	        else {
	            req(p.s, 'R', tSlot, p, 'exit');
	            req(p.t, 'R', sSlot, p, 'entry');
	        }
	    }
	    const ports = new Map();
	    for (const { id, reqs } of portReq.values()) {
	        const units = new Map();
	        reqs.forEach((r, i) => {
	            const uk = r.which === 'exit' && r.plan.e.kind === 'rail' ? `rk:${r.plan.key}` : `solo:${i}`;
	            let u = units.get(uk);
	            if (!u) {
	                u = { sum: 0, n: 0, members: [] };
	                units.set(uk, u);
	            }
	            u.sum += r.farSlot;
	            u.n += 1;
	            u.members.push(r);
	        });
	        const ordered = [...units.values()].sort((a, b) => a.sum / a.n - b.sum / b.n);
	        ordered.forEach((u, i) => {
	            const y = rowY(slot.get(id) ?? 0) + ((i + 1) / (ordered.length + 1)) * nodeH(id);
	            for (const r of u.members) {
	                const rec = ports.get(r.plan) ?? {};
	                rec[r.which] = y;
	                ports.set(r.plan, rec);
	            }
	        });
	    }
	    const gutterItems = Array.from({ length: Math.max(nCols - 1, 1) }, () => []);
	    for (const p of plans) {
	        if (p.route === 'V') {
	            continue;
	        }
	        const py = ports.get(p) ?? {};
	        const exit = py.exit ?? 0;
	        const entry = py.entry ?? 0;
	        const railGroup = p.e.kind === 'rail' ? `${p.s}|${p.e.label}` : null;
	        if (p.cs === p.ct) {
	            const g = Math.max(0, Math.min(p.cs, nCols - 2));
	            p.g1 = { lo: Math.min(exit, entry), hi: Math.max(exit, entry), group: null, plan: p, g };
	            p.route = 'U';
	            gutterItems[g].push(p.g1);
	        }
	        else if (Math.abs(p.cs - p.ct) === 1) {
	            const g = Math.min(p.cs, p.ct);
	            p.g1 = { lo: Math.min(exit, entry), hi: Math.max(exit, entry), group: railGroup, plan: p, g };
	            p.route = 'HVH';
	            gutterItems[g].push(p.g1);
	        }
	        else {
	            const gA = p.cs < p.ct ? p.cs : p.cs - 1;
	            const gB = p.cs < p.ct ? p.ct - 1 : p.ct;
	            const cLo = Math.min(gA, gB) + 1;
	            const cHi = Math.max(gA, gB);
	            let k = Math.max(0, Math.min(maxRows, Math.round((entry - topY) / pitch)));
	            for (const d of [0, 1, -1, 2, -2, 3, -3, 4, -4]) {
	                const kk = k + d;
	                if (kk >= 0 && kk <= maxRows && corridorClear(kk, cLo, cHi)) {
	                    k = kk;
	                    break;
	                }
	            }
	            p.corr = k;
	            const cy = corrY(k);
	            p.g1 = { lo: Math.min(exit, cy), hi: Math.max(exit, cy), group: railGroup, plan: p, g: gA };
	            p.g2 = { lo: Math.min(entry, cy), hi: Math.max(entry, cy), group: null, plan: p, g: gB };
	            p.route = 'CORR';
	            gutterItems[gA].push(p.g1);
	            gutterItems[gB].push(p.g2);
	        }
	    }
	    const laneIdx = new Map();
	    const laneCount = [];
	    gutterItems.forEach((items, g) => {
	        const { out, n } = (0, alloc_lanes_1.allocLanes)(items, constants_1.GUTTER_LANE_PAD);
	        for (const [it, l] of out) {
	            laneIdx.set(it, l);
	        }
	        laneCount[g] = n;
	    });
	    const gutterW = laneCount.map((n) => Math.max(gutterMin, n * laneStep + Math.round(constants_1.GUTTER_EXTRA * S)));
	    const colW = Array.from({ length: nCols }, (_, c) => Math.max(constants_1.NODE_W, ...colBlocks[c].map((id) => nodeW(id))));
	    const colX = [];
	    let cx = constants_1.MARGIN + constants_1.BOARD_PAD;
	    for (let c = 0; c < nCols; c++) {
	        colX[c] = cx;
	        cx += colW[c] + (gutterW[c] ?? 0);
	    }
	    const laneX = (it) => {
	        const g = it.g;
	        const center = colX[g] + colW[g] + (gutterW[g] ?? gutterMin) / 2;
	        return center + ((laneIdx.get(it) ?? 0) - (laneCount[g] - 1) / 2) * laneStep;
	    };
	    const corrItems = new Map();
	    for (const p of plans) {
	        if (p.route !== 'CORR' || p.g1 === undefined || p.g2 === undefined) {
	            continue;
	        }
	        const x1 = laneX(p.g1);
	        const x2 = laneX(p.g2);
	        const k = p.corr ?? 0;
	        const list = corrItems.get(k) ?? [];
	        list.push({ lo: Math.min(x1, x2), hi: Math.max(x1, x2), group: null, plan: p });
	        corrItems.set(k, list);
	    }
	    const corrLane = new Map();
	    for (const k of [...corrItems.keys()].sort((a, b) => a - b)) {
	        const items = corrItems.get(k) ?? [];
	        const { out, n } = (0, alloc_lanes_1.allocLanes)(items, constants_1.CORRIDOR_LANE_PAD);
	        for (const [it, l] of out) {
	            corrLane.set(it.plan, corrY(k) + (l - (n - 1) / 2) * Math.round(constants_1.CORRIDOR_LANE_STEP * S));
	        }
	    }
	    const xy = new Map();
	    for (const b of used) {
	        const c = col.get(b.id) ?? 0;
	        xy.set(b.id, {
	            x: colX[c] + (colW[c] - nodeW(b.id)) / 2,
	            y: rowY(slot.get(b.id) ?? 0),
	            w: nodeW(b.id),
	            h: nodeH(b.id),
	        });
	    }
	    const totW = cx - (gutterW[nCols - 1] ?? 0) + constants_1.BOARD_PAD + constants_1.MARGIN;
	    const H = maxRows * pitch - gapY;
	    const totH = topY + H + constants_1.BOARD_PAD + constants_1.MARGIN;
	    let inner = used.filter((b) => kindOf.get(b.id) !== 'connector' || b.id === hub);
	    if (inner.length === 0) {
	        warnings.push('Every placed block is a connector; the board outline covers them all');
	        inner = used;
	    }
	    const innerIds = new Set(inner.map((b) => b.id));
	    const c0 = Math.min(...inner.map((b) => col.get(b.id) ?? 0));
	    const c1 = Math.max(...inner.map((b) => col.get(b.id) ?? 0));
	    const r0 = Math.min(...inner.map((b) => slot.get(b.id) ?? 0));
	    const r1 = Math.max(...inner.map((b) => (slot.get(b.id) ?? 0) + (hRows.get(b.id) ?? 1) - 1));
	    const bx0 = colX[c0] - constants_1.BOARD_PAD;
	    const bx1 = colX[c1] + colW[c1] + constants_1.BOARD_PAD;
	    let by0 = rowY(r0) - constants_1.BOARD_PAD - 6;
	    let by1 = rowY(r1) + constants_1.NODE_H + constants_1.BOARD_PAD + 6;
	    for (const [p, y] of corrLane) {
	        if (innerIds.has(p.s) && innerIds.has(p.t)) {
	            by0 = Math.min(by0, y - 16);
	            by1 = Math.max(by1, y + 16);
	        }
	    }
	    const layoutEdges = [];
	    const chips = [];
	    for (const p of plans) {
	        const py = ports.get(p) ?? {};
	        const sB = xy.get(p.s);
	        const tB = xy.get(p.t);
	        if (sB === undefined || tB === undefined) {
	            continue;
	        }
	        let points;
	        let lx;
	        let ly;
	        if (p.route === 'V') {
	            const down = sB.y < tB.y;
	            const xc = (Math.max(sB.x, tB.x) + Math.min(sB.x + sB.w, tB.x + tB.w)) / 2;
	            const y1 = down ? sB.y + sB.h : sB.y;
	            const y2 = down ? tB.y : tB.y + tB.h;
	            points = [
	                [xc, y1],
	                [xc, y2],
	            ];
	            lx = xc;
	            ly = (y1 + y2) / 2;
	        }
	        else if (p.route === 'U' && p.g1 !== undefined) {
	            const x = laneX(p.g1);
	            const exit = py.exit ?? 0;
	            const entry = py.entry ?? 0;
	            points = [
	                [sB.x + sB.w, exit],
	                [x, exit],
	                [x, entry],
	                [tB.x + tB.w, entry],
	            ];
	            lx = x;
	            ly = (exit + entry) / 2;
	        }
	        else if (p.route === 'HVH' && p.g1 !== undefined) {
	            const x = laneX(p.g1);
	            const exit = py.exit ?? 0;
	            const entry = py.entry ?? 0;
	            const x1 = p.cs < p.ct ? sB.x + sB.w : sB.x;
	            const x2 = p.cs < p.ct ? tB.x : tB.x + tB.w;
	            points =
	                exit === entry ?
	                    [
	                        [x1, exit],
	                        [x2, exit],
	                    ]
	                    : [
	                        [x1, exit],
	                        [x, exit],
	                        [x, entry],
	                        [x2, entry],
	                    ];
	            lx = x;
	            ly = exit === entry ? exit : (exit + entry) / 2;
	        }
	        else if (p.route === 'CORR' && p.g1 !== undefined && p.g2 !== undefined) {
	            const xA = laneX(p.g1);
	            const xB = laneX(p.g2);
	            const cy = corrLane.get(p) ?? corrY(p.corr ?? 0);
	            const exit = py.exit ?? 0;
	            const entry = py.entry ?? 0;
	            const x1 = p.cs < p.ct ? sB.x + sB.w : sB.x;
	            const x2 = p.cs < p.ct ? tB.x : tB.x + tB.w;
	            points = [
	                [x1, exit],
	                [xA, exit],
	                [xA, cy],
	                [xB, cy],
	                [xB, entry],
	                [x2, entry],
	            ];
	            const w = p.e.label.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
	            lx = (xA + xB) / 2;
	            ly = cy;
	            for (const t of [0.5, 0.3, 0.7, 0.15, 0.85]) {
	                const cx2 = xA + (xB - xA) * t;
	                const hit = used.some((b) => {
	                    const r = xy.get(b.id);
	                    if (r === undefined) {
	                        return false;
	                    }
	                    return cx2 + w / 2 > r.x - 4 && cx2 - w / 2 < r.x + r.w + 4 && cy + 9 > r.y - 4 && cy - 9 < r.y + r.h + 4;
	                });
	                if (!hit) {
	                    lx = cx2;
	                    break;
	                }
	            }
	        }
	        else {
	            continue;
	        }
	        layoutEdges.push({
	            key: p.key,
	            kind: p.e.kind,
	            points,
	            arrowStart: p.dir === 'both',
	            arrowEnd: true,
	            nets: p.e.nets,
	            ...(p.e.kind === 'rail' ? { voltageRank: p.voltageRank ?? null } : {}),
	        });
	        const gkey = `${p.s}|${p.e.label}|${p.e.kind}`;
	        if (p.e.label !== '' && (p.e.kind !== 'rail' || !chips.some((c) => c.gkey === gkey))) {
	            chips.push({
	                x: lx,
	                y: ly,
	                text: p.e.label,
	                gkey,
	                kind: p.e.kind,
	                key: p.key,
	                ...(p.e.kind === 'rail' ? { voltageRank: p.voltageRank ?? null } : {}),
	            });
	        }
	    }
	    chips.sort((a, b) => a.x - b.x || a.y - b.y);
	    for (let i = 0; i < chips.length; i++) {
	        for (let j = 0; j < i; j++) {
	            const a = chips[j];
	            const b = chips[i];
	            const wA = a.text.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
	            const wB = b.text.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
	            if (Math.abs(a.x - b.x) < (wA + wB) / 2 && Math.abs(a.y - b.y) < constants_1.CHIP_H) {
	                b.y = a.y + constants_1.CHIP_DEOVERLAP_DY;
	            }
	        }
	    }
	    const nodes = used.map((b) => {
	        const r = xy.get(b.id) ?? { x: 0, y: 0, w: constants_1.NODE_W, h: constants_1.NODE_H };
	        return {
	            id: b.id,
	            kind: b.kind,
	            x: r.x,
	            y: r.y,
	            w: r.w,
	            h: r.h,
	            big: (hRows.get(b.id) ?? 1) > 1 || (wFac.get(b.id) ?? 1) > 1,
	        };
	    });
	    return {
	        nodes,
	        edges: layoutEdges,
	        chips: chips.map(({ key, kind, x, y, text, voltageRank }) => ({
	            key,
	            kind,
	            x,
	            y,
	            text,
	            ...(voltageRank !== undefined ? { voltageRank } : {}),
	        })),
	        boardOutline: { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 },
	        canvas: { width: totW, height: totH },
	        warnings,
	    };
	}
	
	return computeLayout;
}

var colors$1 = {};

var hasRequiredColors;

function requireColors () {
	if (hasRequiredColors) return colors$1;
	hasRequiredColors = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.VOLT_PALETTE_DARK = exports.VOLT_PALETTE_LIGHT = void 0;
		exports.blockDiagramColorsByName = blockDiagramColorsByName;
		const voltage_1 = requireVoltage();
		exports.VOLT_PALETTE_LIGHT = [
		    '#c62828',
		    '#d84315',
		    '#a85e00',
		    '#2e7d32',
		    '#00796b',
		    '#1565c0',
		    '#6a3ab2',
		];
		exports.VOLT_PALETTE_DARK = [
		    '#ef5350',
		    '#ff7043',
		    '#e0a030',
		    '#66bb6a',
		    '#26a69a',
		    '#42a5f5',
		    '#ab7ce8',
		];
		function blockDiagramColorsByName(theme) {
		    const light = theme === 'light';
		    const palette = light ? exports.VOLT_PALETTE_LIGHT : exports.VOLT_PALETTE_DARK;
		    const colors = {
		        'edge-labels': light ? '#fcfdfd' : '#252b31',
		        'block-name': light ? '#1c242c' : '#e4e9ed',
		        'block-sub': light ? '#5d6b76' : '#9aa7b1',
		        'block-outline-processor': light ? '#1c242c' : '#dfe6ec',
		        'block-outline-power': light ? '#b8860b' : '#d4a72c',
		        'block-outline-connector': light ? '#4285f4' : '#6ba3f8',
		        'block-outline-generic': light ? '#64748b' : '#8b9bb0',
		        'block-fill-processor': light ? '#f1f4f6' : '#232a31',
		        'block-fill-power': light ? '#fdf3d8' : '#332b18',
		        'block-fill-connector': light ? '#e8f0fe' : '#1c2940',
		        'block-fill-generic': light ? '#f1f4f6' : '#232a31',
		        'rail-unranked': light ? '#b8860b' : '#d4a72c',
		        'edge-bus': light ? '#44545f' : '#9fb2bf',
		        'edge-control': light ? '#7c8b97' : '#7f909d',
		        'edge-analog': light ? '#7c8b97' : '#7f909d',
		        'board-label': light ? '#8b99a4' : '#77848f',
		        'board-outline': light ? '#b6c1c9' : '#48545e',
		    };
		    for (let rank = 0; rank < voltage_1.VOLTAGE_RANK_COUNT; rank++) {
		        colors[`rail-rank-${rank}`] = palette[rank];
		    }
		    return colors;
		}
		
	} (colors$1));
	return colors$1;
}

var layers$3 = {};

var hasRequiredLayers$3;

function requireLayers$3 () {
	if (hasRequiredLayers$3) return layers$3;
	hasRequiredLayers$3 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BlockDiagramLayerId = exports.BLOCK_DIAGRAM_CUSTOM_TYPE_PREFIX = exports.BLOCK_DIAGRAM_DESIGN_ID = exports.BLOCK_DIAGRAM_LAYER_SET_ID = void 0;
		exports.buildBlockDiagramLayers = buildBlockDiagramLayers;
		exports.edgeLayerId = edgeLayerId;
		exports.blockLayerIds = blockLayerIds;
		exports.blockCustomTypeName = blockCustomTypeName;
		const sigma_engine_shared_1 = requireDist$1();
		const voltage_1 = requireVoltage();
		exports.BLOCK_DIAGRAM_LAYER_SET_ID = 'block-diagram';
		exports.BLOCK_DIAGRAM_DESIGN_ID = 'block-diagram';
		exports.BLOCK_DIAGRAM_CUSTOM_TYPE_PREFIX = 'cadstrom.blockDiagram';
		var BlockDiagramLayerId;
		(function (BlockDiagramLayerId) {
		    BlockDiagramLayerId[BlockDiagramLayerId["EdgeLabels"] = 1] = "EdgeLabels";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockName"] = 2] = "BlockName";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockSub"] = 3] = "BlockSub";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockOutlineProcessor"] = 4] = "BlockOutlineProcessor";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockOutlinePower"] = 5] = "BlockOutlinePower";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockOutlineConnector"] = 6] = "BlockOutlineConnector";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockOutlineGeneric"] = 7] = "BlockOutlineGeneric";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockFillProcessor"] = 8] = "BlockFillProcessor";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockFillPower"] = 9] = "BlockFillPower";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockFillConnector"] = 10] = "BlockFillConnector";
		    BlockDiagramLayerId[BlockDiagramLayerId["BlockFillGeneric"] = 11] = "BlockFillGeneric";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank0"] = 12] = "RailRank0";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank1"] = 13] = "RailRank1";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank2"] = 14] = "RailRank2";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank3"] = 15] = "RailRank3";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank4"] = 16] = "RailRank4";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank5"] = 17] = "RailRank5";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailRank6"] = 18] = "RailRank6";
		    BlockDiagramLayerId[BlockDiagramLayerId["RailUnranked"] = 19] = "RailUnranked";
		    BlockDiagramLayerId[BlockDiagramLayerId["EdgeBus"] = 20] = "EdgeBus";
		    BlockDiagramLayerId[BlockDiagramLayerId["EdgeControl"] = 21] = "EdgeControl";
		    BlockDiagramLayerId[BlockDiagramLayerId["EdgeAnalog"] = 22] = "EdgeAnalog";
		    BlockDiagramLayerId[BlockDiagramLayerId["BoardLabel"] = 23] = "BoardLabel";
		    BlockDiagramLayerId[BlockDiagramLayerId["BoardOutline"] = 24] = "BoardOutline";
		})(BlockDiagramLayerId || (exports.BlockDiagramLayerId = BlockDiagramLayerId = {}));
		const { SchLayerType, UnknownLayerType } = sigma_engine_shared_1.DesignModels;
		function buildBlockDiagramLayers(showEdgeLabels) {
		    const layers = [
		        {
		            id: BlockDiagramLayerId.EdgeLabels,
		            name: 'edge-labels',
		            friendlyName: 'Edge labels',
		            type: SchLayerType.Label,
		            defaultVisible: showEdgeLabels,
		        },
		        {
		            id: BlockDiagramLayerId.BlockName,
		            name: 'block-name',
		            friendlyName: 'Block names',
		            type: SchLayerType.NameLabel,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockSub,
		            name: 'block-sub',
		            friendlyName: 'Block subtitles',
		            type: SchLayerType.ValueLabel,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockOutlineProcessor,
		            name: 'block-outline-processor',
		            friendlyName: 'Processor outlines',
		            type: SchLayerType.Symbol,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockOutlinePower,
		            name: 'block-outline-power',
		            friendlyName: 'Power outlines',
		            type: SchLayerType.Symbol,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockOutlineConnector,
		            name: 'block-outline-connector',
		            friendlyName: 'Connector outlines',
		            type: SchLayerType.Symbol,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockOutlineGeneric,
		            name: 'block-outline-generic',
		            friendlyName: 'Block outlines',
		            type: SchLayerType.Symbol,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockFillProcessor,
		            name: 'block-fill-processor',
		            friendlyName: 'Processor fills',
		            type: SchLayerType.Background,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockFillPower,
		            name: 'block-fill-power',
		            friendlyName: 'Power fills',
		            type: SchLayerType.Background,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockFillConnector,
		            name: 'block-fill-connector',
		            friendlyName: 'Connector fills',
		            type: SchLayerType.Background,
		            defaultVisible: true,
		        },
		        {
		            id: BlockDiagramLayerId.BlockFillGeneric,
		            name: 'block-fill-generic',
		            friendlyName: 'Block fills',
		            type: SchLayerType.Background,
		            defaultVisible: true,
		        },
		    ];
		    for (let rank = 0; rank < voltage_1.VOLTAGE_RANK_COUNT; rank++) {
		        layers.push({
		            id: BlockDiagramLayerId.RailRank0 + rank,
		            name: `rail-rank-${rank}`,
		            friendlyName: `Power rails (rank ${rank})`,
		            type: SchLayerType.Wire,
		            defaultVisible: true,
		        });
		    }
		    layers.push({
		        id: BlockDiagramLayerId.RailUnranked,
		        name: 'rail-unranked',
		        friendlyName: 'Power rails (unranked)',
		        type: SchLayerType.Wire,
		        defaultVisible: true,
		    }, {
		        id: BlockDiagramLayerId.EdgeBus,
		        name: 'edge-bus',
		        friendlyName: 'Buses',
		        type: SchLayerType.Bus,
		        defaultVisible: true,
		    }, {
		        id: BlockDiagramLayerId.EdgeControl,
		        name: 'edge-control',
		        friendlyName: 'Control signals',
		        type: SchLayerType.Wire,
		        defaultVisible: true,
		    }, {
		        id: BlockDiagramLayerId.EdgeAnalog,
		        name: 'edge-analog',
		        friendlyName: 'Analog signals',
		        type: SchLayerType.Wire,
		        defaultVisible: true,
		    }, {
		        id: BlockDiagramLayerId.BoardLabel,
		        name: 'board-label',
		        friendlyName: 'Board caption',
		        type: UnknownLayerType.Other,
		        defaultVisible: true,
		    }, {
		        id: BlockDiagramLayerId.BoardOutline,
		        name: 'board-outline',
		        friendlyName: 'Board outline',
		        type: UnknownLayerType.Other,
		        defaultVisible: true,
		    });
		    return layers;
		}
		function edgeLayerId(kind, voltageRank) {
		    switch (kind) {
		        case 'rail':
		            return voltageRank === null || voltageRank === undefined ?
		                BlockDiagramLayerId.RailUnranked
		                : BlockDiagramLayerId.RailRank0 + Math.min(voltageRank, voltage_1.VOLTAGE_RANK_COUNT - 1);
		        case 'bus':
		            return BlockDiagramLayerId.EdgeBus;
		        case 'control':
		            return BlockDiagramLayerId.EdgeControl;
		        case 'analog':
		            return BlockDiagramLayerId.EdgeAnalog;
		    }
		}
		function blockLayerIds(kind) {
		    switch (kind) {
		        case 'processor':
		            return { fill: BlockDiagramLayerId.BlockFillProcessor, outline: BlockDiagramLayerId.BlockOutlineProcessor };
		        case 'power':
		            return { fill: BlockDiagramLayerId.BlockFillPower, outline: BlockDiagramLayerId.BlockOutlinePower };
		        case 'connector':
		            return { fill: BlockDiagramLayerId.BlockFillConnector, outline: BlockDiagramLayerId.BlockOutlineConnector };
		        case 'other':
		            return { fill: BlockDiagramLayerId.BlockFillGeneric, outline: BlockDiagramLayerId.BlockOutlineGeneric };
		    }
		}
		function blockCustomTypeName(kind) {
		    return `${exports.BLOCK_DIAGRAM_CUSTOM_TYPE_PREFIX}.${kind === 'other' ? 'block' : kind}`;
		}
		
	} (layers$3));
	return layers$3;
}

var emitProjectModel = {};

var hasRequiredEmitProjectModel;

function requireEmitProjectModel () {
	if (hasRequiredEmitProjectModel) return emitProjectModel;
	hasRequiredEmitProjectModel = 1;
	Object.defineProperty(emitProjectModel, "__esModule", { value: true });
	emitProjectModel.emitProjectModel = emitProjectModel$1;
	const constants_1 = requireConstants$1();
	const layers_1 = requireLayers$3();
	function emitProjectModel$1(blocks, layout, opts) {
	    const blockById = new Map(blocks.map((b) => [b.id, b]));
	    const drawings = {};
	    const addDrawing = (layer, drawing) => {
	        (drawings[layer] = drawings[layer] ?? []).push(drawing);
	    };
	    addDrawing(layers_1.BlockDiagramLayerId.BoardOutline, {
	        type: 'rect',
	        start: [layout.boardOutline.x, layout.boardOutline.y],
	        end: [layout.boardOutline.x + layout.boardOutline.w, layout.boardOutline.y + layout.boardOutline.h],
	        strokeWidth: constants_1.BOARD_OUTLINE_STROKE_W,
	    });
	    addDrawing(layers_1.BlockDiagramLayerId.BoardLabel, {
	        type: 'text',
	        value: 'BOARD',
	        pos: [layout.boardOutline.x + 14, layout.boardOutline.y + 10],
	        size: constants_1.BOARD_LABEL_FONT_SIZE,
	        justify: [-1, 0],
	        thickness: 1,
	    });
	    for (const edge of layout.edges) {
	        const layer = (0, layers_1.edgeLayerId)(edge.kind, edge.voltageRank);
	        const style = constants_1.EDGE_STYLE[edge.kind];
	        const net = edge.nets[0];
	        for (let i = 1; i < edge.points.length; i++) {
	            const [x1, y1] = edge.points[i - 1];
	            const [x2, y2] = edge.points[i];
	            if (x1 === x2 && y1 === y2) {
	                continue;
	            }
	            addDrawing(layer, {
	                type: 'segment',
	                start: [x1, y1],
	                end: [x2, y2],
	                width: style.width,
	                style: style.lineStyle,
	                ...(net !== undefined ? { net } : {}),
	                metadata: { blockDiagramEdgeKey: edge.key },
	            });
	        }
	        if (opts.showArrows) {
	            for (const arrow of arrowheads(edge)) {
	                addDrawing(layer, { ...arrow, ...(net !== undefined ? { net } : {}) });
	            }
	        }
	    }
	    for (const chip of layout.chips) {
	        const w = chip.text.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
	        addDrawing(layers_1.BlockDiagramLayerId.EdgeLabels, {
	            type: 'rect',
	            start: [chip.x - w / 2, chip.y - constants_1.CHIP_H / 2],
	            end: [chip.x + w / 2, chip.y + constants_1.CHIP_H / 2],
	            strokeWidth: 0,
	            filled: true,
	            metadata: { blockDiagramEdgeKey: chip.key },
	        });
	        addDrawing(layers_1.BlockDiagramLayerId.EdgeLabels, {
	            type: 'text',
	            value: chip.text,
	            pos: [chip.x, chip.y],
	            size: constants_1.CHIP_FONT_SIZE,
	            justify: [0, 0],
	            thickness: 1,
	            contrastWithLayer: true,
	            metadata: { blockDiagramEdgeKey: chip.key },
	        });
	    }
	    const components = {};
	    const modules = [];
	    const projectModules = [];
	    for (const node of layout.nodes) {
	        const block = blockById.get(node.id);
	        if (block === undefined) {
	            continue;
	        }
	        const componentRef = `bd_cmp_${node.id}`;
	        const footprintRef = `bd_fp_${node.id}`;
	        const layers = (0, layers_1.blockLayerIds)(node.kind);
	        const strokeW = node.kind === 'processor' ? constants_1.PROCESSOR_STROKE_W : constants_1.BLOCK_STROKE_W;
	        const nameSize = node.big ? constants_1.NAME_FONT_SIZE_BIG : constants_1.NAME_FONT_SIZE;
	        const subSize = node.big ? constants_1.SUB_FONT_SIZE_BIG : constants_1.SUB_FONT_SIZE;
	        const maxLabelW = node.w - 2 * constants_1.BLOCK_LABEL_PAD_X;
	        const name = (0, constants_1.truncateTextToWidth)(block.name, nameSize, maxLabelW);
	        const sub = block.sub !== undefined ? (0, constants_1.truncateTextToWidth)(block.sub, subSize, maxLabelW) : undefined;
	        const half = [node.w / 2, node.h / 2];
	        const symbolDrawings = [
	            {
	                layers: [layers.fill],
	                drawing: {
	                    type: 'rect',
	                    start: [-half[0], -half[1]],
	                    end: [half[0], half[1]],
	                    strokeWidth: 0,
	                    filled: true,
	                },
	            },
	            {
	                layers: [layers.outline],
	                drawing: {
	                    type: 'rect',
	                    start: [-half[0], -half[1]],
	                    end: [half[0], half[1]],
	                    strokeWidth: strokeW,
	                },
	            },
	            {
	                layers: [layers_1.BlockDiagramLayerId.BlockName],
	                drawing: {
	                    type: 'text',
	                    value: name,
	                    pos: [0, sub !== undefined ? -9 : 0],
	                    size: nameSize,
	                    justify: [0, 0],
	                    thickness: 1.5,
	                },
	            },
	        ];
	        if (sub !== undefined) {
	            symbolDrawings.push({
	                layers: [layers_1.BlockDiagramLayerId.BlockSub],
	                drawing: {
	                    type: 'text',
	                    value: sub,
	                    pos: [0, 9],
	                    size: subSize,
	                    justify: [0, 0],
	                    thickness: 1,
	                },
	            });
	        }
	        components[componentRef] = {
	            name: componentRef,
	            numPins: 0,
	            numPads: 0,
	            footprints: [],
	            symbols: [
	                {
	                    ref: footprintRef,
	                    name: footprintRef,
	                    drawings: symbolDrawings,
	                    pins: [],
	                },
	            ],
	            signals: [],
	            isVirtual: true,
	        };
	        modules.push({
	            ref: node.id,
	            footprintRef,
	            componentRef,
	            center: [node.x + half[0], node.y + half[1]],
	            layer: 'F',
	            transform: { angle: 0, mirrored: [false, false] },
	            name: node.id,
	            pinNets: {},
	            drawProperties: {},
	            customTypeName: (0, layers_1.blockCustomTypeName)(node.kind),
	        });
	        projectModules.push({
	            name: node.id,
	            instances: [{ ref: node.id, designId: layers_1.BLOCK_DIAGRAM_DESIGN_ID }],
	            componentRef,
	            value: block.sub,
	            hideInBom: true,
	        });
	    }
	    const nets = {};
	    for (const edge of layout.edges) {
	        for (const net of edge.nets) {
	            nets[net] = nets[net] ?? { name: net, connections: [] };
	        }
	    }
	    const bbox = { minx: 0, miny: 0, maxx: layout.canvas.width, maxy: layout.canvas.height };
	    const design = {
	        modelType: 'schematic',
	        designId: layers_1.BLOCK_DIAGRAM_DESIGN_ID,
	        designName: opts.title,
	        sourceFile: '',
	        drawings,
	        edges_bbox: bbox,
	        initial_zoombox: bbox,
	        layerSet: layers_1.BLOCK_DIAGRAM_LAYER_SET_ID,
	        warnings: [],
	        nets,
	        sheetIndex: 0,
	        modules,
	        ports: {},
	    };
	    return {
	        type: 'cadstrom',
	        name: opts.title,
	        layerSets: { [layers_1.BLOCK_DIAGRAM_LAYER_SET_ID]: (0, layers_1.buildBlockDiagramLayers)(opts.showEdgeLabels) },
	        designs: { [layers_1.BLOCK_DIAGRAM_DESIGN_ID]: design },
	        components,
	        modules: projectModules,
	        warnings: [],
	        processingResults: { type: 'project-processing', message: '', innerResults: [] },
	        globalNets: {},
	    };
	}
	function arrowheads(edge) {
	    const out = [];
	    const pts = edge.points;
	    if (pts.length < 2) {
	        return out;
	    }
	    if (edge.arrowEnd) {
	        out.push(arrowAt(pts[pts.length - 2], pts[pts.length - 1]));
	    }
	    if (edge.arrowStart) {
	        out.push(arrowAt(pts[1], pts[0]));
	    }
	    return out;
	}
	function arrowAt(from, tip) {
	    const dx = tip[0] - from[0];
	    const dy = tip[1] - from[1];
	    const len = Math.hypot(dx, dy);
	    const ux = len === 0 ? 1 : dx / len;
	    const uy = len === 0 ? 0 : dy / len;
	    const bx = tip[0] - ux * constants_1.ARROW_LEN;
	    const by = tip[1] - uy * constants_1.ARROW_LEN;
	    return {
	        type: 'polygon',
	        filled: true,
	        width: 0,
	        points: [
	            [tip[0], tip[1]],
	            [bx - uy * constants_1.ARROW_HALF_W, by + ux * constants_1.ARROW_HALF_W],
	            [bx + uy * constants_1.ARROW_HALF_W, by - ux * constants_1.ARROW_HALF_W],
	        ],
	    };
	}
	
	return emitProjectModel;
}

var compile = {};

var hasRequiredCompile;

function requireCompile () {
	if (hasRequiredCompile) return compile;
	hasRequiredCompile = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DEFAULT_BLOCK_DIAGRAM_KNOBS = exports.SPACING_KNOB_MAX = exports.SPACING_KNOB_MIN = exports.LAYOUT_ENGINE_VERSION = void 0;
		exports.resolveBlockDiagramKnobs = resolveBlockDiagramKnobs;
		exports.computeBlockDiagramLayout = computeBlockDiagramLayout;
		exports.compileBlockDiagram = compileBlockDiagram;
		const compute_layout_1 = requireComputeLayout();
		const voltage_1 = requireVoltage();
		const derive_variant_1 = requireDeriveVariant();
		const emit_project_model_1 = requireEmitProjectModel();
		exports.LAYOUT_ENGINE_VERSION = '1.2.0';
		exports.SPACING_KNOB_MIN = 0.9;
		exports.SPACING_KNOB_MAX = 1.3;
		exports.DEFAULT_BLOCK_DIAGRAM_KNOBS = {
		    spacing: exports.SPACING_KNOB_MAX,
		    showEdgeLabels: false,
		    showArrows: false,
		};
		function resolveBlockDiagramKnobs(doc, knobs) {
		    return {
		        spacing: knobs?.spacing ?? doc?.placement?.spacing ?? exports.DEFAULT_BLOCK_DIAGRAM_KNOBS.spacing,
		        showEdgeLabels: knobs?.showEdgeLabels ?? exports.DEFAULT_BLOCK_DIAGRAM_KNOBS.showEdgeLabels,
		        showArrows: knobs?.showArrows ?? exports.DEFAULT_BLOCK_DIAGRAM_KNOBS.showArrows,
		    };
		}
		function computeBlockDiagramLayout(doc, variant, knobs) {
		    const edges = (0, derive_variant_1.deriveVariant)(doc, variant);
		    const scale = (0, voltage_1.computeVoltageScale)(variant === 'full' ? edges : (0, derive_variant_1.deriveVariant)(doc, 'full'));
		    const { spacing } = resolveBlockDiagramKnobs(doc, knobs);
		    return (0, compute_layout_1.computeLayout)(doc.blocks, edges, doc.placement, spacing, scale);
		}
		function compileBlockDiagram(doc, variant, knobs) {
		    const resolved = resolveBlockDiagramKnobs(doc, knobs);
		    const layout = computeBlockDiagramLayout(doc, variant, resolved);
		    const voltageScale = (0, voltage_1.computeVoltageScale)((0, derive_variant_1.deriveVariant)(doc, 'full'));
		    const projectModel = (0, emit_project_model_1.emitProjectModel)(doc.blocks, layout, {
		        title: doc.title,
		        showEdgeLabels: resolved.showEdgeLabels,
		        showArrows: resolved.showArrows,
		    });
		    return { projectModel, layout, voltageScale };
		}
		
	} (compile));
	return compile;
}

var hasRequiredBlockDiagram;

function requireBlockDiagram () {
	if (hasRequiredBlockDiagram) return BlockDiagram;
	hasRequiredBlockDiagram = 1;
	(function (exports) {
		var __createBinding = (BlockDiagram && BlockDiagram.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (BlockDiagram && BlockDiagram.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBlockDiagramDocument(), exports);
		__exportStar(requireCollapsedEdge(), exports);
		__exportStar(requireDeriveVariant(), exports);
		__exportStar(requireVoltage(), exports);
		__exportStar(requireLayoutTypes(), exports);
		__exportStar(requireAssignColumns(), exports);
		__exportStar(requireOrderRows(), exports);
		__exportStar(requireAllocLanes(), exports);
		__exportStar(requireComputeLayout(), exports);
		__exportStar(requireConstants$1(), exports);
		__exportStar(requireColors(), exports);
		__exportStar(requireLayers$3(), exports);
		__exportStar(requireEmitProjectModel(), exports);
		__exportStar(requireCompile(), exports);
		
	} (BlockDiagram));
	return BlockDiagram;
}

var CadstromConfig = {};

var Schema = {};

var CadstromV1ConfigSchema = {};

var hasRequiredCadstromV1ConfigSchema;

function requireCadstromV1ConfigSchema () {
	if (hasRequiredCadstromV1ConfigSchema) return CadstromV1ConfigSchema;
	hasRequiredCadstromV1ConfigSchema = 1;
	Object.defineProperty(CadstromV1ConfigSchema, "__esModule", { value: true });
	CadstromV1ConfigSchema.default = {
	    $id: 'http://cadstrom.io/api/v1/edrc/schema/cadstromv1_1configschema.json',
	    $schema: 'http://json-schema.org/draft-07/schema#',
	    additionalProperties: false,
	    definitions: {
	        BuildConfigurationCheckId: {
	            enum: ['footprintReference', 'insights'],
	            type: 'string',
	        },
	        CadSemanticFileType: {
	            enum: ['board', 'schematic', 'lib', 'dru', 'project', 'other'],
	            type: 'string',
	        },
	        CadstromV1_1BoardConfig: {
	            additionalProperties: false,
	            properties: {
	                approvals: {
	                    items: {
	                        type: 'string',
	                    },
	                    type: 'array',
	                },
	                dru: {
	                    anyOf: [
	                        {
	                            type: 'string',
	                        },
	                        {
	                            $ref: '#/definitions/ICadstromV1_1DruConfig',
	                        },
	                    ],
	                },
	                file: {
	                    type: 'string',
	                },
	                name: {
	                    type: 'string',
	                },
	            },
	            required: ['file'],
	            type: 'object',
	        },
	        CadstromV1_1IncludeConfig: {
	            additionalProperties: false,
	            properties: {
	                file: {
	                    type: 'string',
	                },
	                name: {
	                    type: 'string',
	                },
	                type: {
	                    $ref: '#/definitions/CadSemanticFileType',
	                },
	            },
	            required: ['file', 'type'],
	            type: 'object',
	        },
	        CadstromV1_1ProjectConfig: {
	            additionalProperties: false,
	            properties: {
	                boards: {
	                    items: {
	                        $ref: '#/definitions/CadstromV1_1BoardConfig',
	                    },
	                    type: 'array',
	                },
	                checks: {
	                    $ref: '#/definitions/IBuildCheck',
	                },
	                includes: {
	                    items: {
	                        $ref: '#/definitions/CadstromV1_1IncludeConfig',
	                    },
	                    type: 'array',
	                },
	                schematics: {
	                    items: {
	                        $ref: '#/definitions/CadstromV1_1SchematicConfig',
	                    },
	                    type: 'array',
	                },
	                type: {
	                    enum: ['eagle', 'kicad', 'altium'],
	                    type: 'string',
	                },
	            },
	            required: ['type'],
	            type: 'object',
	        },
	        CadstromV1_1SchematicConfig: {
	            additionalProperties: false,
	            properties: {
	                file: {
	                    type: 'string',
	                },
	                lib: {
	                    type: 'string',
	                },
	                name: {
	                    type: 'string',
	                },
	            },
	            required: ['file'],
	            type: 'object',
	        },
	        IBuildCheck: {
	            additionalProperties: false,
	            properties: {
	                footprintReference: {
	                    $ref: '#/definitions/IBuildCheckConfiguration%3CIFootprintReferenceCheckSettings%3E',
	                },
	                insights: {
	                    $ref: '#/definitions/IBuildCheckConfiguration%3CIInsightsCheckSettings%3E',
	                },
	            },
	            type: 'object',
	        },
	        'IBuildCheckConfiguration<IFootprintReferenceCheckSettings>': {
	            additionalProperties: false,
	            properties: {
	                enabled: {
	                    type: 'boolean',
	                },
	                id: {
	                    $ref: '#/definitions/BuildConfigurationCheckId',
	                },
	                settings: {
	                    $ref: '#/definitions/IFootprintReferenceCheckSettings',
	                    description: 'The configured settings for the check',
	                },
	            },
	            required: ['id', 'enabled', 'settings'],
	            type: 'object',
	        },
	        'IBuildCheckConfiguration<IInsightsCheckSettings>': {
	            additionalProperties: false,
	            properties: {
	                enabled: {
	                    type: 'boolean',
	                },
	                id: {
	                    $ref: '#/definitions/BuildConfigurationCheckId',
	                },
	                settings: {
	                    $ref: '#/definitions/IInsightsCheckSettings',
	                    description: 'The configured settings for the check',
	                },
	            },
	            required: ['id', 'enabled', 'settings'],
	            type: 'object',
	        },
	        ICadstromV1_1DruConfig: {
	            additionalProperties: false,
	            properties: {
	                file: {
	                    type: 'string',
	                },
	                layers: {
	                    anyOf: [
	                        {
	                            type: 'string',
	                        },
	                        {
	                            items: {
	                                type: 'string',
	                            },
	                            type: 'array',
	                        },
	                    ],
	                },
	            },
	            required: ['file'],
	            type: 'object',
	        },
	        IFootprintReferenceCheckSettings: {
	            additionalProperties: false,
	            properties: {
	                mechanicalCompatibilityCheck: {
	                    additionalProperties: false,
	                    properties: {
	                        enabled: {
	                            description: 'Whether the mechanical compatibility check is enabled.',
	                            type: 'boolean',
	                        },
	                        mechanicalCompatibilityWarningTolerance: {
	                            description: 'Configures the maximum tolerance (as a percentage of pad area) for the mechanical compatibility check. Differences greater than this tolerance will be reported as an error.',
	                            type: 'number',
	                        },
	                        referenceComponentSpec: {
	                            description: 'The IPC specification to use for the reference component.\n\nThis setting is only configurable for users with the advancedFootprintReferenceCheck plan feature. This setting is ignored if the mechanical compatibility check is disabled.\n\nMeanings:\n- ipc-mmc: Maximum Material Condition\n- ipc-nmc: Nominal Material Condition\n- ipc-lmc: Least Material Condition',
	                            enum: ['ipc-mmc', 'ipc-nmc', 'ipc-lmc'],
	                            type: 'string',
	                        },
	                    },
	                    required: ['enabled', 'mechanicalCompatibilityWarningTolerance', 'referenceComponentSpec'],
	                    type: 'object',
	                },
	                pinAssignmentCheck: {
	                    additionalProperties: false,
	                    properties: {
	                        enabled: {
	                            description: 'Whether the pin assignment check is enabled.',
	                            type: 'boolean',
	                        },
	                    },
	                    required: ['enabled'],
	                    type: 'object',
	                },
	            },
	            type: 'object',
	        },
	        IInsightsCheckSettings: {
	            additionalProperties: false,
	            properties: {
	                enabled: {
	                    type: 'boolean',
	                },
	            },
	            type: 'object',
	        },
	    },
	    properties: {
	        project: {
	            $ref: '#/definitions/CadstromV1_1ProjectConfig',
	        },
	        version: {
	            type: ['number', 'string'],
	        },
	    },
	    required: ['version', 'project'],
	    type: 'object',
	};
	
	return CadstromV1ConfigSchema;
}

var hasRequiredSchema;

function requireSchema () {
	if (hasRequiredSchema) return Schema;
	hasRequiredSchema = 1;
	Object.defineProperty(Schema, "__esModule", { value: true });
	Schema.CadstromV1ConfigSchema = void 0;
	const CadstromV1ConfigSchema_1 = requireCadstromV1ConfigSchema();
	Schema.CadstromV1ConfigSchema = CadstromV1ConfigSchema_1.default;
	
	return Schema;
}

var ConfigV1 = {};

var hasRequiredConfigV1;

function requireConfigV1 () {
	if (hasRequiredConfigV1) return ConfigV1;
	hasRequiredConfigV1 = 1;
	Object.defineProperty(ConfigV1, "__esModule", { value: true });
	
	return ConfigV1;
}

var hasRequiredCadstromConfig;

function requireCadstromConfig () {
	if (hasRequiredCadstromConfig) return CadstromConfig;
	hasRequiredCadstromConfig = 1;
	(function (exports) {
		var __createBinding = (CadstromConfig && CadstromConfig.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (CadstromConfig && CadstromConfig.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireSchema(), exports);
		__exportStar(requireConfigV1(), exports);
		
	} (CadstromConfig));
	return CadstromConfig;
}

var Comparison = {};

var valueComparison = {};

var hasRequiredValueComparison;

function requireValueComparison () {
	if (hasRequiredValueComparison) return valueComparison;
	hasRequiredValueComparison = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.COMPOSITE_KEY_SEPARATOR = void 0;
		exports.normalizeForComparison = normalizeForComparison;
		exports.compositeKey = compositeKey;
		exports.indexByKey = indexByKey;
		const defaultColumnNormalizer = (_column, value) => normalizeForComparison(value);
		function normalizeForComparison(value) {
		    if (value === null || value === undefined) {
		        return '';
		    }
		    if (typeof value === 'number') {
		        return Number.isFinite(value) ? String(value) : '';
		    }
		    if (typeof value === 'string') {
		        const trimmed = value.trim();
		        const asNumber = Number(trimmed);
		        if (trimmed !== '' && !Number.isNaN(asNumber)) {
		            return String(asNumber);
		        }
		        return trimmed;
		    }
		    if (typeof value === 'boolean') {
		        return value ? 'true' : 'false';
		    }
		    return JSON.stringify(value);
		}
		exports.COMPOSITE_KEY_SEPARATOR = '\x1F';
		function compositeKey(row, alignOnKeys, normalize = defaultColumnNormalizer) {
		    const r = row;
		    return alignOnKeys.map((k) => normalize(k, r[k], r)).join(exports.COMPOSITE_KEY_SEPARATOR);
		}
		function indexByKey(rows, alignOnKeys, normalize = defaultColumnNormalizer, onDuplicate) {
		    const map = new Map();
		    for (const row of rows) {
		        const key = compositeKey(row, alignOnKeys, normalize);
		        const kept = map.get(key);
		        if (kept !== undefined) {
		            onDuplicate?.(key, kept, row);
		            continue;
		        }
		        map.set(key, row);
		    }
		    return map;
		}
		
	} (valueComparison));
	return valueComparison;
}

var siComparison = {};

var symbolicComparison = {};

var signalNameNormalization = {};

var hasRequiredSignalNameNormalization;

function requireSignalNameNormalization () {
	if (hasRequiredSignalNameNormalization) return signalNameNormalization;
	hasRequiredSignalNameNormalization = 1;
	Object.defineProperty(signalNameNormalization, "__esModule", { value: true });
	signalNameNormalization.normalizeSignalNameForComparison = normalizeSignalNameForComparison;
	signalNameNormalization.foldSignalNameNotation = foldSignalNameNotation;
	function normalizeSignalNameForComparison(name) {
	    if (name === '') {
	        return '';
	    }
	    return name
	        .trim()
	        .replace(/\u2212/g, '-')
	        .toLowerCase();
	}
	const POLARITY_MARKS = /[#~!/*]/g;
	const DASH_VARIANTS = /[‐‑‒–—―−]/g;
	const LATEX_OVERLINE = /\\overline\{([^}]*)\}/g;
	function foldSignalNameNotation(name) {
	    if (name === '') {
	        return '';
	    }
	    return (normalizeSignalNameForComparison(name)
	        .replace(LATEX_OVERLINE, '$1')
	        .normalize('NFD')
	        .replace(/[̀-̲ͯ̅]/g, '')
	        .replace(POLARITY_MARKS, '')
	        .replace(DASH_VARIANTS, '-')
	        .replace(/[\s_-]/g, ''));
	}
	
	return signalNameNormalization;
}

var hasRequiredSymbolicComparison;

function requireSymbolicComparison () {
	if (hasRequiredSymbolicComparison) return symbolicComparison;
	hasRequiredSymbolicComparison = 1;
	Object.defineProperty(symbolicComparison, "__esModule", { value: true });
	symbolicComparison.foldSymbolicValue = foldSymbolicValue;
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const MULTIPLICATION_SIGNS = new Set(['×', '·', '⋅', '*']);
	const MINUS_SIGNS = new Set(['-', '−']);
	const OPERATORS = new Set(['+', '-', '*', '/', '(', ')']);
	var TokenKind;
	(function (TokenKind) {
	    TokenKind["Operand"] = "operand";
	    TokenKind["Operator"] = "operator";
	    TokenKind["OpenParen"] = "open_paren";
	    TokenKind["CloseParen"] = "close_paren";
	})(TokenKind || (TokenKind = {}));
	function isInfixPosition(previous, hasFollowing) {
	    if (previous === undefined || !hasFollowing) {
	        return false;
	    }
	    return previous.kind === TokenKind.Operand || previous.kind === TokenKind.CloseParen;
	}
	function unitSpellings(declaration) {
	    const spellings = [declaration.short, declaration.long, ...(declaration.alternateNames ?? [])];
	    return spellings
	        .filter((spelling) => spelling !== undefined && spelling !== '')
	        .sort((a, b) => b.length - a.length);
	}
	function stripTrailingUnit(trimmed, declaration) {
	    const lowered = trimmed.toLowerCase();
	    for (const spelling of unitSpellings(declaration)) {
	        const suffix = spelling.toLowerCase();
	        if (!lowered.endsWith(suffix) || lowered.length === suffix.length) {
	            continue;
	        }
	        const head = trimmed.slice(0, trimmed.length - suffix.length);
	        if (/\d\s*$/.test(head)) {
	            return head;
	        }
	    }
	    return trimmed;
	}
	const UNARY_SIGNS = new Set(['+', '-']);
	function isWellFormed(tokens) {
	    let depth = 0;
	    let operandDue = true;
	    for (const token of tokens) {
	        switch (token.kind) {
	            case TokenKind.Operator:
	                if (operandDue && !UNARY_SIGNS.has(token.text)) {
	                    return false;
	                }
	                operandDue = true;
	                break;
	            case TokenKind.OpenParen:
	                if (!operandDue) {
	                    return false;
	                }
	                depth += 1;
	                break;
	            case TokenKind.CloseParen:
	                if (operandDue || depth === 0) {
	                    return false;
	                }
	                depth -= 1;
	                break;
	            case TokenKind.Operand:
	                if (!operandDue && token.spaced) {
	                    return false;
	                }
	                operandDue = false;
	                break;
	        }
	    }
	    return !operandDue && depth === 0;
	}
	function tokenize(input) {
	    const tokens = [];
	    let index = 0;
	    let spaced = false;
	    function push(token) {
	        tokens.push({ ...token, spaced });
	        spaced = false;
	    }
	    while (index < input.length) {
	        const character = input[index] ?? '';
	        if (/\s/.test(character)) {
	            spaced = true;
	            index += 1;
	            continue;
	        }
	        if (MULTIPLICATION_SIGNS.has(character)) {
	            push({ kind: TokenKind.Operator, text: '*' });
	            index += 1;
	            continue;
	        }
	        if (MINUS_SIGNS.has(character)) {
	            push({ kind: TokenKind.Operator, text: '-' });
	            index += 1;
	            continue;
	        }
	        if (OPERATORS.has(character)) {
	            const kind = character === '(' ? TokenKind.OpenParen
	                : character === ')' ? TokenKind.CloseParen
	                    : TokenKind.Operator;
	            push({ kind, text: character });
	            index += 1;
	            continue;
	        }
	        const number = /^\d*\.?\d+/.exec(input.slice(index));
	        if (number !== null) {
	            const magnitude = Number(number[0]);
	            if (!Number.isFinite(magnitude)) {
	                return null;
	            }
	            push({ kind: TokenKind.Operand, text: String(magnitude) });
	            index += number[0].length;
	            continue;
	        }
	        const identifier = /^[A-Za-z_][A-Za-z0-9_.]*/.exec(input.slice(index));
	        if (identifier === null) {
	            return null;
	        }
	        const text = identifier[0];
	        const rest = input.slice(index + text.length).trim();
	        if ((text === 'x' || text === 'X') && isInfixPosition(tokens[tokens.length - 1], rest !== '')) {
	            push({ kind: TokenKind.Operator, text: '*' });
	        }
	        else {
	            push({ kind: TokenKind.Operand, text: (0, signal_name_normalization_1.normalizeSignalNameForComparison)(text) });
	        }
	        index += text.length;
	    }
	    return tokens.length > 0 && isWellFormed(tokens) ? tokens : null;
	}
	function canonicalizeProductOrder(tokens) {
	    const [left, operator, right] = tokens;
	    if (tokens.length !== 3 ||
	        left === undefined ||
	        right === undefined ||
	        operator?.text !== '*' ||
	        left.kind !== TokenKind.Operand ||
	        right.kind !== TokenKind.Operand) {
	        return tokens;
	    }
	    return left.text <= right.text ? tokens : [right, operator, left];
	}
	function foldSymbolicValue(raw, declaration) {
	    const trimmed = raw?.trim() ?? '';
	    if (trimmed === '') {
	        return '';
	    }
	    const tokens = tokenize(stripTrailingUnit(trimmed, declaration));
	    if (tokens === null) {
	        return trimmed;
	    }
	    return canonicalizeProductOrder(tokens)
	        .map((token) => token.text)
	        .join('');
	}
	
	return symbolicComparison;
}

var hasRequiredSiComparison;

function requireSiComparison () {
	if (hasRequiredSiComparison) return siComparison;
	hasRequiredSiComparison = 1;
	Object.defineProperty(siComparison, "__esModule", { value: true });
	siComparison.formatSIValueWithDeclaration = formatSIValueWithDeclaration;
	siComparison.formatSIValue = formatSIValue;
	siComparison.formatSIOrSymbolicValueWithDeclaration = formatSIOrSymbolicValueWithDeclaration;
	siComparison.formatSIOrSymbolicValue = formatSIOrSymbolicValue;
	siComparison.canonicalizeVariancePrefix = canonicalizeVariancePrefix;
	siComparison.formatVariance = formatVariance;
	const sigma_engine_shared_1 = requireDist$1();
	const symbolic_comparison_1 = requireSymbolicComparison();
	function formatSIValueWithDeclaration(raw, declaration) {
	    const trimmed = raw?.trim() ?? '';
	    if (!trimmed) {
	        return '';
	    }
	    try {
	        const parsed = sigma_engine_shared_1.Utils.parseNumericalSIValue(trimmed, declaration);
	        if (parsed === undefined) {
	            return trimmed;
	        }
	        const decimalPlaces = Math.max(0, Math.round(-Math.log10(declaration.epsilon ?? 1e-9)));
	        return String(Number(parsed.toFixed(decimalPlaces)));
	    }
	    catch {
	        return trimmed;
	    }
	}
	function formatSIValue(raw, characteristic) {
	    return formatSIValueWithDeclaration(raw, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic]);
	}
	function formatSIOrSymbolicValueWithDeclaration(raw, declaration) {
	    const asSI = formatSIValueWithDeclaration(raw, declaration);
	    return Number.isFinite(Number(asSI)) && asSI !== '' ? asSI : (0, symbolic_comparison_1.foldSymbolicValue)(asSI, declaration);
	}
	function formatSIOrSymbolicValue(raw, characteristic) {
	    return formatSIOrSymbolicValueWithDeclaration(raw, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic]);
	}
	function canonicalizeVariancePrefix(raw) {
	    return (raw?.trim() ?? '').replace(/^\+\/-\s*/, '±');
	}
	function formatVariance(raw) {
	    const trimmed = canonicalizeVariancePrefix(raw);
	    if (!trimmed) {
	        return '';
	    }
	    if (/^±$/.test(trimmed)) {
	        return '';
	    }
	    return trimmed;
	}
	
	return siComparison;
}

var hasRequiredComparison;

function requireComparison () {
	if (hasRequiredComparison) return Comparison;
	hasRequiredComparison = 1;
	(function (exports) {
		var __createBinding = (Comparison && Comparison.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Comparison && Comparison.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireValueComparison(), exports);
		__exportStar(requireSiComparison(), exports);
		__exportStar(requireSymbolicComparison(), exports);
		
	} (Comparison));
	return Comparison;
}

var ComponentInferenceCsv = {};

var csvRow = {};

var hasRequiredCsvRow;

function requireCsvRow () {
	if (hasRequiredCsvRow) return csvRow;
	hasRequiredCsvRow = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.componentPropertiesCsvTableRowSchema = exports.CSV_COLUMN_NAMES = void 0;
		const z = /*@__PURE__*/ requireZod();
		exports.CSV_COLUMN_NAMES = {
		    SIGNAL_NAME: 'Signal Name',
		    PINS: 'Pins',
		    ATTRIBUTES: 'Attributes',
		    IS_GLOBAL_POWER: 'Is Global Power',
		    IS_GLOBAL_GROUND: 'Is Global Ground',
		    POWER_RAIL_OVERRIDE: 'Power Rail Override',
		    GROUND_RAIL_OVERRIDE: 'Ground Rail Override',
		    ABSOLUTE_MAXIMUM_RATINGS: 'Absolute Maximum Ratings (V)',
		    OPERATING_CONDITIONS: 'Operating Conditions (V)',
		    FUNCTIONS: 'Functions (Bus Classes)',
		    SIGNAL_DRIVE_CHARACTERISTICS: 'Signal Drive Characteristics',
		};
		exports.componentPropertiesCsvTableRowSchema = z.object({
		    [exports.CSV_COLUMN_NAMES.SIGNAL_NAME]: z.string(),
		    [exports.CSV_COLUMN_NAMES.PINS]: z.string(),
		    [exports.CSV_COLUMN_NAMES.ATTRIBUTES]: z.string(),
		    [exports.CSV_COLUMN_NAMES.IS_GLOBAL_POWER]: z.string(),
		    [exports.CSV_COLUMN_NAMES.IS_GLOBAL_GROUND]: z.string(),
		    [exports.CSV_COLUMN_NAMES.POWER_RAIL_OVERRIDE]: z.string(),
		    [exports.CSV_COLUMN_NAMES.GROUND_RAIL_OVERRIDE]: z.string(),
		    [exports.CSV_COLUMN_NAMES.ABSOLUTE_MAXIMUM_RATINGS]: z.string(),
		    [exports.CSV_COLUMN_NAMES.OPERATING_CONDITIONS]: z.string(),
		    [exports.CSV_COLUMN_NAMES.FUNCTIONS]: z.string(),
		    [exports.CSV_COLUMN_NAMES.SIGNAL_DRIVE_CHARACTERISTICS]: z.string(),
		});
		
	} (csvRow));
	return csvRow;
}

var hasRequiredComponentInferenceCsv;

function requireComponentInferenceCsv () {
	if (hasRequiredComponentInferenceCsv) return ComponentInferenceCsv;
	hasRequiredComponentInferenceCsv = 1;
	(function (exports) {
		var __createBinding = (ComponentInferenceCsv && ComponentInferenceCsv.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ComponentInferenceCsv && ComponentInferenceCsv.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireCsvRow(), exports);
		
	} (ComponentInferenceCsv));
	return ComponentInferenceCsv;
}

var Consensus = {};

var sliceKey = {};

var hasRequiredSliceKey;

function requireSliceKey () {
	if (hasRequiredSliceKey) return sliceKey;
	hasRequiredSliceKey = 1;
	Object.defineProperty(sliceKey, "__esModule", { value: true });
	sliceKey.SliceKeySchema = sliceKey.SliceKey = void 0;
	const z = /*@__PURE__*/ requireZod();
	var SliceKey;
	(function (SliceKey) {
	    SliceKey["SigmaModelType"] = "sigma_model_type";
	    SliceKey["ElementSigmaModelType"] = "element_sigma_model_type";
	    SliceKey["Parameters"] = "parameters";
	    SliceKey["ElementParameters"] = "element_parameters";
	    SliceKey["SignalSet"] = "signal_set";
	    SliceKey["Modes"] = "modes";
	    SliceKey["SignalAttributes"] = "signal_attributes";
	    SliceKey["Pinout"] = "pinout";
	    SliceKey["PinAssignments"] = "pin_assignments";
	    SliceKey["FloatingBehaviour"] = "floating_behaviour";
	    SliceKey["SignalTies"] = "signal_ties";
	    SliceKey["AmrBoundaries"] = "amr_boundaries";
	    SliceKey["OcBoundaries"] = "oc_boundaries";
	    SliceKey["DriveModes"] = "drive_modes";
	    SliceKey["Resources"] = "resources";
	    SliceKey["Functions"] = "functions";
	    SliceKey["FunctionSignalMapping"] = "function_signal_mapping";
	    SliceKey["Structure"] = "structure";
	    SliceKey["SignalMapping"] = "signal_mapping";
	    SliceKey["NcPins"] = "nc_pins";
	})(SliceKey || (sliceKey.SliceKey = SliceKey = {}));
	sliceKey.SliceKeySchema = z.nativeEnum(SliceKey);
	
	return sliceKey;
}

var propertyKey = {};

var hasRequiredPropertyKey;

function requirePropertyKey () {
	if (hasRequiredPropertyKey) return propertyKey;
	hasRequiredPropertyKey = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SLICE_PROPERTY_KEYS = exports.PropertyKeySchema = exports.PropertyKey = void 0;
		exports.propertyKeyForSlice = propertyKeyForSlice;
		const z = /*@__PURE__*/ requireZod();
		const slice_key_1 = requireSliceKey();
		var PropertyKey;
		(function (PropertyKey) {
		    PropertyKey["SigmaModelType"] = "sigma_model_type";
		    PropertyKey["Parameters"] = "parameters";
		    PropertyKey["Modes"] = "modes";
		    PropertyKey["SignalAttributes"] = "signal_attributes";
		    PropertyKey["ActiveLow"] = "active_low";
		    PropertyKey["Pinout"] = "pinout";
		    PropertyKey["FloatingBehaviour"] = "floating_behaviour";
		    PropertyKey["SignalTies"] = "signal_ties";
		    PropertyKey["AmrBoundaries"] = "amr_boundaries";
		    PropertyKey["OcBoundaries"] = "oc_boundaries";
		    PropertyKey["DriveModes"] = "drive_modes";
		    PropertyKey["Resources"] = "resources";
		    PropertyKey["Functions"] = "functions";
		})(PropertyKey || (exports.PropertyKey = PropertyKey = {}));
		exports.PropertyKeySchema = z.nativeEnum(PropertyKey);
		exports.SLICE_PROPERTY_KEYS = {
		    [slice_key_1.SliceKey.SigmaModelType]: PropertyKey.SigmaModelType,
		    [slice_key_1.SliceKey.ElementSigmaModelType]: PropertyKey.SigmaModelType,
		    [slice_key_1.SliceKey.Parameters]: PropertyKey.Parameters,
		    [slice_key_1.SliceKey.ElementParameters]: PropertyKey.Parameters,
		    [slice_key_1.SliceKey.SignalSet]: PropertyKey.SignalAttributes,
		    [slice_key_1.SliceKey.Modes]: PropertyKey.Modes,
		    [slice_key_1.SliceKey.SignalAttributes]: PropertyKey.SignalAttributes,
		    [slice_key_1.SliceKey.Pinout]: PropertyKey.Pinout,
		    [slice_key_1.SliceKey.PinAssignments]: PropertyKey.Pinout,
		    [slice_key_1.SliceKey.FloatingBehaviour]: PropertyKey.FloatingBehaviour,
		    [slice_key_1.SliceKey.SignalTies]: PropertyKey.SignalTies,
		    [slice_key_1.SliceKey.AmrBoundaries]: PropertyKey.AmrBoundaries,
		    [slice_key_1.SliceKey.OcBoundaries]: PropertyKey.OcBoundaries,
		    [slice_key_1.SliceKey.DriveModes]: PropertyKey.DriveModes,
		    [slice_key_1.SliceKey.Resources]: PropertyKey.Resources,
		    [slice_key_1.SliceKey.Functions]: PropertyKey.Functions,
		    [slice_key_1.SliceKey.FunctionSignalMapping]: PropertyKey.Functions,
		    [slice_key_1.SliceKey.Structure]: PropertyKey.SigmaModelType,
		    [slice_key_1.SliceKey.SignalMapping]: PropertyKey.Pinout,
		    [slice_key_1.SliceKey.NcPins]: PropertyKey.Pinout,
		};
		function propertyKeyForSlice(sliceKey) {
		    const parsed = slice_key_1.SliceKeySchema.safeParse(sliceKey);
		    if (!parsed.success) {
		        throw new Error(`Unknown slice key: ${sliceKey}`);
		    }
		    return exports.SLICE_PROPERTY_KEYS[parsed.data];
		}
		
	} (propertyKey));
	return propertyKey;
}

var sliceVersion = {};

var hasRequiredSliceVersion;

function requireSliceVersion () {
	if (hasRequiredSliceVersion) return sliceVersion;
	hasRequiredSliceVersion = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SLICE_VERSIONS = void 0;
		exports.sliceVersionFor = sliceVersionFor;
		const slice_key_1 = requireSliceKey();
		exports.SLICE_VERSIONS = {
		    [slice_key_1.SliceKey.SigmaModelType]: 1,
		    [slice_key_1.SliceKey.ElementSigmaModelType]: 1,
		    [slice_key_1.SliceKey.Parameters]: 2,
		    [slice_key_1.SliceKey.ElementParameters]: 2,
		    [slice_key_1.SliceKey.SignalSet]: 1,
		    [slice_key_1.SliceKey.Modes]: 1,
		    [slice_key_1.SliceKey.SignalAttributes]: 1,
		    [slice_key_1.SliceKey.Pinout]: 1,
		    [slice_key_1.SliceKey.PinAssignments]: 1,
		    [slice_key_1.SliceKey.FloatingBehaviour]: 1,
		    [slice_key_1.SliceKey.SignalTies]: 2,
		    [slice_key_1.SliceKey.AmrBoundaries]: 1,
		    [slice_key_1.SliceKey.OcBoundaries]: 1,
		    [slice_key_1.SliceKey.DriveModes]: 1,
		    [slice_key_1.SliceKey.Resources]: 1,
		    [slice_key_1.SliceKey.Functions]: 1,
		    [slice_key_1.SliceKey.FunctionSignalMapping]: 1,
		    [slice_key_1.SliceKey.Structure]: 1,
		    [slice_key_1.SliceKey.SignalMapping]: 2,
		    [slice_key_1.SliceKey.NcPins]: 2,
		};
		function sliceVersionFor(sliceKey) {
		    return exports.SLICE_VERSIONS[sliceKey];
		}
		
	} (sliceVersion));
	return sliceVersion;
}

var consensus_types = {};

var hasRequiredConsensus_types;

function requireConsensus_types () {
	if (hasRequiredConsensus_types) return consensus_types;
	hasRequiredConsensus_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ReviewItemSchema = exports.SuggestedValueSchema = exports.ReviewQuestion = exports.TriagedAnchorTallySchema = exports.TriagePath = exports.ScoredCandidateSchema = exports.CandidateTallySchema = exports.MEMBERSHIP_COLUMN = exports.AnchorSchema = exports.FieldAnchorSchema = exports.RowAnchorSchema = exports.AnchorKind = void 0;
		exports.anchorKey = anchorKey;
		const z = /*@__PURE__*/ requireZod();
		var AnchorKind;
		(function (AnchorKind) {
		    AnchorKind["Row"] = "row";
		    AnchorKind["Field"] = "field";
		})(AnchorKind || (exports.AnchorKind = AnchorKind = {}));
		exports.RowAnchorSchema = z.object({
		    kind: z.literal(AnchorKind.Row),
		    rowKey: z.string(),
		});
		exports.FieldAnchorSchema = z.object({
		    kind: z.literal(AnchorKind.Field),
		    rowKey: z.string(),
		    column: z.string(),
		});
		exports.AnchorSchema = z.discriminatedUnion('kind', [
		    exports.RowAnchorSchema,
		    exports.FieldAnchorSchema,
		]);
		function anchorKey(anchor) {
		    return anchor.kind === AnchorKind.Field ? `${anchor.rowKey}\x1F${anchor.column}` : `${anchor.rowKey}\x1F`;
		}
		exports.MEMBERSHIP_COLUMN = '__membership__';
		exports.CandidateTallySchema = z.object({
		    value: z.unknown().optional(),
		    normalizedValue: z.string(),
		    voteCount: z.number(),
		    proposedBy: z.string().array(),
		});
		exports.ScoredCandidateSchema = exports.CandidateTallySchema.extend({
		    confidence: z.number(),
		    confidenceRationale: z.string().optional(),
		    denominator: z.number().optional(),
		});
		var TriagePath;
		(function (TriagePath) {
		    TriagePath["AutoAccept"] = "auto_accept";
		    TriagePath["ExpertReview"] = "expert_review";
		})(TriagePath || (exports.TriagePath = TriagePath = {}));
		exports.TriagedAnchorTallySchema = z.object({
		    anchor: exports.AnchorSchema,
		    triagePath: z.nativeEnum(TriagePath),
		    candidates: exports.ScoredCandidateSchema.array(),
		    resolved: z.unknown().optional(),
		});
		var ReviewQuestion;
		(function (ReviewQuestion) {
		    ReviewQuestion["ShouldExist"] = "should_exist";
		    ReviewQuestion["SpecifyValue"] = "specify_value";
		})(ReviewQuestion || (exports.ReviewQuestion = ReviewQuestion = {}));
		exports.SuggestedValueSchema = z.object({
		    value: z.unknown().optional(),
		    voteCount: z.number(),
		    confidence: z.number(),
		    confidenceRationale: z.string().optional(),
		});
		exports.ReviewItemSchema = z.object({
		    anchor: exports.AnchorSchema,
		    question: z.nativeEnum(ReviewQuestion),
		    suggestedValues: exports.SuggestedValueSchema.array(),
		});
		
	} (consensus_types));
	return consensus_types;
}

var comparisonSpec = {};

var hasRequiredComparisonSpec;

function requireComparisonSpec () {
	if (hasRequiredComparisonSpec) return comparisonSpec;
	hasRequiredComparisonSpec = 1;
	Object.defineProperty(comparisonSpec, "__esModule", { value: true });
	comparisonSpec.SetAlignment = comparisonSpec.CellAlignment = comparisonSpec.AlignmentStrategy = comparisonSpec.AlignmentMode = comparisonSpec.ComparisonSpec = void 0;
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	class ComparisonSpec {
	    constructor() {
	        this.membershipContestable = true;
	        this.autoResolveColumns = new Map();
	    }
	    rowKeyOf(row) {
	        const normalize = (column, value, rowContext) => this.normalize(column, value, rowContext);
	        if (this.alignment instanceof CellAlignment) {
	            return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => normalize(column, value, rowContext));
	        }
	        return this.alignment instanceof SetAlignment ? this.alignment.identityOf(row, normalize) : JSON.stringify(row);
	    }
	    rowCellsFromKey(rowKey) {
	        return this.alignment instanceof SetAlignment ? this.alignment.cellsOfIdentity(rowKey) : null;
	    }
	    normalize(_column, value, _row) {
	        return (0, value_comparison_1.normalizeForComparison)(value);
	    }
	    admitsValue(_column, _value) {
	        return true;
	    }
	    canonicalizeKeys(row) {
	        return row;
	    }
	}
	comparisonSpec.ComparisonSpec = ComparisonSpec;
	var AlignmentMode;
	(function (AlignmentMode) {
	    AlignmentMode["Cell"] = "cell";
	    AlignmentMode["Set"] = "set";
	})(AlignmentMode || (comparisonSpec.AlignmentMode = AlignmentMode = {}));
	class AlignmentStrategy {
	}
	comparisonSpec.AlignmentStrategy = AlignmentStrategy;
	class CellAlignment extends AlignmentStrategy {
	    constructor(keyColumns, valueColumns) {
	        super();
	        this.keyColumns = keyColumns;
	        this.valueColumns = valueColumns;
	        this.mode = AlignmentMode.Cell;
	    }
	}
	comparisonSpec.CellAlignment = CellAlignment;
	class SetAlignment extends AlignmentStrategy {
	    constructor() {
	        super(...arguments);
	        this.mode = AlignmentMode.Set;
	    }
	    static { this.IDENTITY_SCHEMA = z.array(z.tuple([z.string(), z.string()])); }
	    identityOf(row, normalize, columns) {
	        const keys = (columns ? columns.map(String) : Object.keys(row)).sort();
	        return JSON.stringify(keys.map((k) => [k, normalize(k, row[k], row)]));
	    }
	    cellsOfIdentity(identity) {
	        let parsed;
	        try {
	            parsed = JSON.parse(identity);
	        }
	        catch {
	            return null;
	        }
	        const pairs = SetAlignment.IDENTITY_SCHEMA.safeParse(parsed);
	        return pairs.success ? Object.fromEntries(pairs.data) : null;
	    }
	}
	comparisonSpec.SetAlignment = SetAlignment;
	
	return comparisonSpec;
}

var confidencePolicy = {};

var hasRequiredConfidencePolicy;

function requireConfidencePolicy () {
	if (hasRequiredConfidencePolicy) return confidencePolicy;
	hasRequiredConfidencePolicy = 1;
	Object.defineProperty(confidencePolicy, "__esModule", { value: true });
	confidencePolicy.ProportionalConfidencePolicy = confidencePolicy.GradedConfidencePolicy = confidencePolicy.UnanimousConfidencePolicy = confidencePolicy.ConfidencePolicy = void 0;
	class ConfidencePolicy {
	    static { this.defaultMinReviewers = 2; }
	    constructor(minReviewers = ConfidencePolicy.defaultMinReviewers) {
	        if (!Number.isInteger(minReviewers) || minReviewers < 1) {
	            throw new RangeError(`ConfidencePolicy minReviewers must be an integer >= 1, but received ${minReviewers}.`);
	        }
	        this.minReviewers = minReviewers;
	    }
	    label(tally, totalReviewers) {
	        const belowQuorum = totalReviewers < this.minReviewers;
	        const subQuorumScore = {
	            confidence: 0,
	            confidenceRationale: `Only ${totalReviewers} reviewer(s); at least ${this.minReviewers} are required for consensus.`,
	        };
	        return {
	            anchor: tally.anchor,
	            candidates: tally.candidates.map((candidate) => ({
	                ...candidate,
	                ...(belowQuorum ? subQuorumScore : this.scoreCandidate(candidate, totalReviewers)),
	                denominator: totalReviewers,
	            })),
	        };
	    }
	    formatShare(voteCount, totalReviewers) {
	        return `${voteCount} of ${totalReviewers} reviewer(s)`;
	    }
	}
	confidencePolicy.ConfidencePolicy = ConfidencePolicy;
	class UnanimousConfidencePolicy extends ConfidencePolicy {
	    constructor(options = {}) {
	        super(options.minReviewers);
	    }
	    scoreCandidate(candidate, totalReviewers) {
	        if (candidate.voteCount === totalReviewers) {
	            return { confidence: 1, confidenceRationale: `All ${totalReviewers} reviewers proposed this value.` };
	        }
	        const share = this.formatShare(candidate.voteCount, totalReviewers);
	        return { confidence: 0, confidenceRationale: `Only ${share} proposed this; unanimity required.` };
	    }
	}
	confidencePolicy.UnanimousConfidencePolicy = UnanimousConfidencePolicy;
	class GradedConfidencePolicy extends ConfidencePolicy {
	    static { this.defaultMajorityConfidence = 0.7; }
	    constructor(options = {}) {
	        super(options.minReviewers);
	        const majorityConfidence = options.majorityConfidence ?? GradedConfidencePolicy.defaultMajorityConfidence;
	        if (!Number.isFinite(majorityConfidence) || majorityConfidence < 0 || majorityConfidence > 1) {
	            throw new RangeError(`GradedConfidencePolicy majorityConfidence must be a finite number within [0, 1], ` +
	                `but received ${majorityConfidence}.`);
	        }
	        this.majorityConfidence = majorityConfidence;
	    }
	    scoreCandidate(candidate, totalReviewers) {
	        const ratio = candidate.voteCount / totalReviewers;
	        if (ratio === 1) {
	            return { confidence: 1, confidenceRationale: `All ${totalReviewers} reviewers agreed.` };
	        }
	        const share = this.formatShare(candidate.voteCount, totalReviewers);
	        if (ratio > 0.5) {
	            const percent = Math.round(this.majorityConfidence * 100);
	            return {
	                confidence: this.majorityConfidence,
	                confidenceRationale: `A majority (${share}) chose this; held at ${percent}% until unanimous.`,
	            };
	        }
	        return { confidence: 0, confidenceRationale: `Only ${share} chose this (minority).` };
	    }
	}
	confidencePolicy.GradedConfidencePolicy = GradedConfidencePolicy;
	class ProportionalConfidencePolicy extends ConfidencePolicy {
	    constructor(options = {}) {
	        super(options.minReviewers);
	    }
	    scoreCandidate(candidate, totalReviewers) {
	        const share = this.formatShare(candidate.voteCount, totalReviewers);
	        const percent = Math.round((candidate.voteCount / totalReviewers) * 100);
	        return {
	            confidence: candidate.voteCount / totalReviewers,
	            confidenceRationale: `${share} chose this value (${percent}%).`,
	        };
	    }
	}
	confidencePolicy.ProportionalConfidencePolicy = ProportionalConfidencePolicy;
	
	return confidencePolicy;
}

var triagePolicy = {};

var hasRequiredTriagePolicy;

function requireTriagePolicy () {
	if (hasRequiredTriagePolicy) return triagePolicy;
	hasRequiredTriagePolicy = 1;
	Object.defineProperty(triagePolicy, "__esModule", { value: true });
	triagePolicy.ThresholdTriagePolicy = triagePolicy.TriagePolicy = void 0;
	const consensus_types_1 = requireConsensus_types();
	class TriagePolicy {
	}
	triagePolicy.TriagePolicy = TriagePolicy;
	class ThresholdTriagePolicy extends TriagePolicy {
	    constructor(min = 1, minQuorum = 1) {
	        super();
	        this.min = min;
	        this.minQuorum = minQuorum;
	    }
	    triagePathFor(tally) {
	        return tally.candidates.some((candidate) => this.meetsAutoAcceptBar(candidate)) ?
	            consensus_types_1.TriagePath.AutoAccept
	            : consensus_types_1.TriagePath.ExpertReview;
	    }
	    meetsAutoAcceptBar(candidate) {
	        return candidate.confidence >= this.min && candidate.voteCount >= this.minQuorum;
	    }
	}
	triagePolicy.ThresholdTriagePolicy = ThresholdTriagePolicy;
	
	return triagePolicy;
}

var autoResolvePolicy = {};

var hasRequiredAutoResolvePolicy;

function requireAutoResolvePolicy () {
	if (hasRequiredAutoResolvePolicy) return autoResolvePolicy;
	hasRequiredAutoResolvePolicy = 1;
	Object.defineProperty(autoResolvePolicy, "__esModule", { value: true });
	autoResolvePolicy.PluralityPreferNonEmptyAutoResolvePolicy = autoResolvePolicy.AutoResolvePolicy = void 0;
	class AutoResolvePolicy {
	}
	autoResolvePolicy.AutoResolvePolicy = AutoResolvePolicy;
	class PluralityPreferNonEmptyAutoResolvePolicy extends AutoResolvePolicy {
	    pick(candidates) {
	        const nonEmpty = candidates.filter((candidate) => candidate.normalizedValue !== '');
	        const pool = nonEmpty.length > 0 ? nonEmpty : candidates;
	        let winner = pool[0];
	        for (const candidate of pool) {
	            if (winner === undefined || candidate.voteCount > winner.voteCount) {
	                winner = candidate;
	            }
	        }
	        return winner?.value;
	    }
	}
	autoResolvePolicy.PluralityPreferNonEmptyAutoResolvePolicy = PluralityPreferNonEmptyAutoResolvePolicy;
	
	return autoResolvePolicy;
}

var consensusAssembler = {};

var hasRequiredConsensusAssembler;

function requireConsensusAssembler () {
	if (hasRequiredConsensusAssembler) return consensusAssembler;
	hasRequiredConsensusAssembler = 1;
	Object.defineProperty(consensusAssembler, "__esModule", { value: true });
	consensusAssembler.ConsensusAssembler = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const consensus_types_1 = requireConsensus_types();
	class ConsensusAssembler {
	    assemble(triaged, spec, totalReviewers) {
	        const groups = this.groupByRow(triaged);
	        return {
	            table: this.materializeTable(groups, spec),
	            annotations: triaged,
	            reviewPlan: this.buildReviewPlan(groups, spec),
	            autoAcceptAll: triaged.length > 0 && triaged.every((tally) => tally.triagePath === consensus_types_1.TriagePath.AutoAccept),
	            totalReviewers,
	        };
	    }
	    groupByRow(triaged) {
	        const groups = [];
	        const byKey = new Map();
	        for (const tally of triaged) {
	            const { rowKey } = tally.anchor;
	            let group = byKey.get(rowKey);
	            if (!group) {
	                group = { rowKey, membership: undefined, fields: new Map() };
	                byKey.set(rowKey, group);
	                groups.push(group);
	            }
	            if (tally.anchor.kind === consensus_types_1.AnchorKind.Row) {
	                group.membership = tally;
	            }
	            else {
	                group.fields.set(tally.anchor.column, tally);
	            }
	        }
	        return groups;
	    }
	    materializeTable(groups, spec) {
	        const rows = [];
	        for (const group of groups) {
	            const representative = group.membership?.candidates[0]?.value;
	            const base = this.toRecord(representative);
	            if (group.fields.size === 0) {
	                if (representative !== undefined) {
	                    rows.push(spec.canonicalizeKeys(base));
	                }
	                continue;
	            }
	            for (const [column, field] of group.fields) {
	                base[column] = this.resolveCell(field);
	            }
	            rows.push(spec.canonicalizeKeys(base));
	        }
	        return spec.fromRows(rows);
	    }
	    toRecord(value) {
	        return value !== null && typeof value === 'object' ? { ...value } : {};
	    }
	    resolveCell(field) {
	        if (field.triagePath === consensus_types_1.TriagePath.AutoAccept) {
	            return field.resolved;
	        }
	        return this.plurality(field.candidates).value;
	    }
	    plurality(candidates) {
	        let max = -1;
	        let winners = [];
	        for (const candidate of candidates) {
	            if (candidate.voteCount > max) {
	                max = candidate.voteCount;
	                winners = [candidate];
	            }
	            else if (candidate.voteCount === max) {
	                winners.push(candidate);
	            }
	        }
	        if (winners.length === 1) {
	            return { value: winners[0].value, tie: false };
	        }
	        const firstSorted = [...winners].sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a.normalizedValue, b.normalizedValue))[0];
	        return { value: firstSorted?.value, tie: true };
	    }
	    buildReviewPlan(groups, spec) {
	        const items = [];
	        for (const group of groups) {
	            const { membership } = group;
	            if (spec.membershipContestable && membership && membership.triagePath === consensus_types_1.TriagePath.ExpertReview) {
	                items.push({
	                    anchor: membership.anchor,
	                    question: consensus_types_1.ReviewQuestion.ShouldExist,
	                    suggestedValues: this.toSuggestedValues(membership.candidates),
	                });
	            }
	            for (const field of group.fields.values()) {
	                if (field.triagePath !== consensus_types_1.TriagePath.AutoAccept) {
	                    items.push(this.specifyValueItem(field));
	                }
	            }
	        }
	        return items;
	    }
	    toSuggestedValues(candidates) {
	        return candidates.map((candidate) => ({
	            value: candidate.value,
	            voteCount: candidate.voteCount,
	            confidence: candidate.confidence,
	            confidenceRationale: candidate.confidenceRationale,
	        }));
	    }
	    specifyValueItem(field) {
	        return {
	            anchor: field.anchor,
	            question: consensus_types_1.ReviewQuestion.SpecifyValue,
	            suggestedValues: this.toSuggestedValues(field.candidates),
	        };
	    }
	}
	consensusAssembler.ConsensusAssembler = ConsensusAssembler;
	
	return consensusAssembler;
}

var multiReviewerConsensus_service = {};

var hasRequiredMultiReviewerConsensus_service;

function requireMultiReviewerConsensus_service () {
	if (hasRequiredMultiReviewerConsensus_service) return multiReviewerConsensus_service;
	hasRequiredMultiReviewerConsensus_service = 1;
	Object.defineProperty(multiReviewerConsensus_service, "__esModule", { value: true });
	multiReviewerConsensus_service.MultiReviewerConsensusService = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const value_comparison_1 = requireValueComparison();
	const comparison_spec_1 = requireComparisonSpec();
	const consensus_types_1 = requireConsensus_types();
	class MultiReviewerConsensusService {
	    static { this.serviceName = MultiReviewerConsensusService.name; }
	    constructor(logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	        this.logger = logger;
	    }
	    analyze(submissions, spec) {
	        if (submissions.length === 0) {
	            this.logger.error('Analyze_NoReviewers', { service: MultiReviewerConsensusService.serviceName });
	            throw new Error('Consensus requires at least one reviewer submission, but received none.');
	        }
	        const reviewerIds = submissions.map(([reviewerId]) => reviewerId);
	        if (new Set(reviewerIds).size !== reviewerIds.length) {
	            this.logger.error('Analyze_DuplicateReviewerIds', {
	                service: MultiReviewerConsensusService.serviceName,
	                reviewerIds,
	            });
	            throw new Error('Consensus requires a unique reviewer id per submission, but received duplicate ids — ' +
	                'one reviewer would otherwise be counted as several, inflating votes and N.');
	        }
	        const totalReviewers = submissions.length;
	        const alignment = spec.alignment;
	        let anchorTallies;
	        if (alignment instanceof comparison_spec_1.SetAlignment) {
	            anchorTallies = this.analyzeSet(submissions, spec, alignment);
	        }
	        else if (alignment instanceof comparison_spec_1.CellAlignment) {
	            anchorTallies = this.analyzeCell(submissions, spec, alignment);
	        }
	        else {
	            this.logger.error('Analyze_UnknownAlignmentMode', {
	                service: MultiReviewerConsensusService.serviceName,
	                mode: alignment.mode,
	            });
	            throw new Error(`Unknown alignment mode: ${alignment.mode}`);
	        }
	        return { totalReviewers, anchorTallies };
	    }
	    score(report, policy, membershipDenominators) {
	        const membershipByRow = new Map();
	        for (const tally of report.anchorTallies) {
	            if (tally.anchor.kind === consensus_types_1.AnchorKind.Row) {
	                membershipByRow.set(tally.anchor.rowKey, tally.candidates[0]?.voteCount ?? 0);
	            }
	        }
	        return report.anchorTallies.map((tally) => {
	            const denominator = tally.anchor.kind === consensus_types_1.AnchorKind.Field ?
	                (membershipByRow.get(tally.anchor.rowKey) ?? report.totalReviewers)
	                : (membershipDenominators?.get(tally.anchor.rowKey) ?? report.totalReviewers);
	            return policy.label(tally, denominator);
	        });
	    }
	    scopedMembershipDenominators(report, spec, holdings) {
	        const scopeColumns = spec.membershipScopeColumns;
	        const denominators = new Map();
	        if (scopeColumns === undefined || scopeColumns.length === 0) {
	            return denominators;
	        }
	        const holdingSets = [...holdings.values()];
	        for (const tally of report.anchorTallies) {
	            if (tally.anchor.kind !== consensus_types_1.AnchorKind.Row) {
	                continue;
	            }
	            const membership = tally.candidates[0];
	            const row = membership?.value;
	            if (membership === undefined || row === null || typeof row !== 'object') {
	                continue;
	            }
	            const scopeValues = scopeColumns.map((column) => spec.normalize(column, row[column], row));
	            const holders = holdingSets.filter((held) => scopeValues.every((value) => held.has(value))).length;
	            if (holders < membership.voteCount) {
	                this.logger.warn('ScopedMembershipDenominators_HolderInvariantViolated', {
	                    service: MultiReviewerConsensusService.serviceName,
	                    rowKey: tally.anchor.rowKey,
	                    holders,
	                    voteCount: membership.voteCount,
	                });
	                continue;
	            }
	            denominators.set(tally.anchor.rowKey, holders);
	        }
	        return denominators;
	    }
	    triage(scored, policy) {
	        return scored.map((tally) => this.routeTally(tally, policy));
	    }
	    acceptUnfilledFields(triaged) {
	        return triaged.map((tally) => {
	            if (tally.anchor.kind !== consensus_types_1.AnchorKind.Field || tally.triagePath === consensus_types_1.TriagePath.AutoAccept) {
	                return tally;
	            }
	            const allBlank = tally.candidates.length > 0 && tally.candidates.every((candidate) => candidate.normalizedValue === '');
	            if (!allBlank) {
	                return tally;
	            }
	            return {
	                anchor: tally.anchor,
	                triagePath: consensus_types_1.TriagePath.AutoAccept,
	                candidates: tally.candidates,
	                resolved: tally.candidates[0].value,
	            };
	        });
	    }
	    autoResolve(triaged, autoResolveColumns) {
	        if (autoResolveColumns.size === 0) {
	            return triaged;
	        }
	        return triaged.map((tally) => {
	            if (tally.anchor.kind !== consensus_types_1.AnchorKind.Field) {
	                return tally;
	            }
	            const policy = autoResolveColumns.get(tally.anchor.column);
	            if (policy === undefined) {
	                return tally;
	            }
	            return {
	                anchor: tally.anchor,
	                triagePath: consensus_types_1.TriagePath.AutoAccept,
	                candidates: tally.candidates,
	                resolved: policy.pick(tally.candidates),
	            };
	        });
	    }
	    assemble(triaged, spec, assembler, totalReviewers) {
	        return assembler.assemble(triaged, spec, totalReviewers);
	    }
	    run(submissions, config) {
	        const report = this.analyze(submissions, config.spec);
	        const membershipDenominators = config.membershipHoldings === undefined ?
	            undefined
	            : this.scopedMembershipDenominators(report, config.spec, config.membershipHoldings);
	        const scored = this.score(report, config.confidencePolicy, membershipDenominators);
	        const triaged = this.acceptUnfilledFields(this.triage(scored, config.triagePolicy));
	        const resolved = this.autoResolve(triaged, this.stringKeyedAutoResolve(config.spec.autoResolveColumns));
	        return this.assemble(resolved, config.spec, config.assembler, report.totalReviewers);
	    }
	    stringKeyedAutoResolve(columns) {
	        const byName = new Map();
	        for (const [column, policy] of columns) {
	            byName.set(String(column), policy);
	        }
	        return byName;
	    }
	    routeTally(tally, policy) {
	        const path = policy.triagePathFor(tally);
	        if (path !== consensus_types_1.TriagePath.AutoAccept) {
	            return { anchor: tally.anchor, triagePath: path, candidates: tally.candidates };
	        }
	        const clearers = tally.candidates.filter((candidate) => policy.meetsAutoAcceptBar(candidate));
	        if (clearers.length === 1) {
	            return {
	                anchor: tally.anchor,
	                triagePath: consensus_types_1.TriagePath.AutoAccept,
	                candidates: tally.candidates,
	                resolved: clearers[0].value,
	            };
	        }
	        return { anchor: tally.anchor, triagePath: consensus_types_1.TriagePath.ExpertReview, candidates: tally.candidates };
	    }
	    analyzeCell(submissions, spec, alignment) {
	        const keyColumns = alignment.keyColumns.map(String);
	        const valueColumns = alignment.valueColumns.map(String);
	        const normalize = (column, value, row) => spec.normalize(column, value, row);
	        const perReviewer = submissions.map(([reviewerId, data]) => ({
	            reviewerId,
	            index: (0, value_comparison_1.indexByKey)(spec.toRows(data), keyColumns, normalize, (rowKey, kept, dropped) => this.logger.warn('AnalyzeCell_DuplicateRowKey', {
	                service: MultiReviewerConsensusService.serviceName,
	                reviewerId,
	                rowKey,
	                divergentValues: valueColumns
	                    .filter((column) => kept[column] !== dropped[column])
	                    .map((column) => ({ column, kept: kept[column], dropped: dropped[column] })),
	            })),
	        }));
	        const orderedKeys = this.collectOrderedKeys(perReviewer);
	        const anchorTallies = [];
	        for (const rowKey of orderedKeys) {
	            const reviewersWithRow = perReviewer.filter((entry) => entry.index.has(rowKey));
	            const rowsForKey = reviewersWithRow
	                .map((entry) => entry.index.get(rowKey))
	                .filter((row) => row !== undefined);
	            const representativeRow = this.pickRepresentative(rowsForKey);
	            anchorTallies.push({
	                anchor: { kind: consensus_types_1.AnchorKind.Row, rowKey },
	                candidates: [
	                    {
	                        value: representativeRow,
	                        normalizedValue: rowKey,
	                        voteCount: reviewersWithRow.length,
	                        proposedBy: reviewersWithRow.map((entry) => entry.reviewerId),
	                    },
	                ],
	            });
	            for (const column of valueColumns) {
	                anchorTallies.push({
	                    anchor: { kind: consensus_types_1.AnchorKind.Field, rowKey, column },
	                    candidates: this.tallyFieldCandidates(reviewersWithRow, rowKey, column, normalize),
	                });
	            }
	        }
	        return anchorTallies;
	    }
	    analyzeSet(submissions, spec, alignment) {
	        const normalize = (column, value, row) => spec.normalize(column, value, row);
	        const perReviewerRows = submissions.map(([reviewerId, data]) => [reviewerId, spec.toRows(data)]);
	        const columns = this.collectColumnUnion(perReviewerRows.flatMap(([, rows]) => rows));
	        const ordered = [];
	        const byIdentity = new Map();
	        for (const [reviewerId, rows] of perReviewerRows) {
	            const seenThisReviewer = new Set();
	            for (const row of rows) {
	                const identity = alignment.identityOf(row, normalize, columns);
	                if (seenThisReviewer.has(identity)) {
	                    this.logger.warn('AnalyzeSet_DuplicateIdentity', {
	                        service: MultiReviewerConsensusService.serviceName,
	                        reviewerId,
	                        identity,
	                    });
	                    continue;
	                }
	                seenThisReviewer.add(identity);
	                let member = byIdentity.get(identity);
	                if (!member) {
	                    member = { rows: [], proposedBy: [] };
	                    byIdentity.set(identity, member);
	                    ordered.push(identity);
	                }
	                member.rows.push(row);
	                member.proposedBy.push(reviewerId);
	            }
	        }
	        return ordered.map((identity) => {
	            const member = byIdentity.get(identity);
	            return {
	                anchor: { kind: consensus_types_1.AnchorKind.Row, rowKey: identity },
	                candidates: [
	                    {
	                        value: this.pickRepresentative(member.rows),
	                        normalizedValue: identity,
	                        voteCount: member.proposedBy.length,
	                        proposedBy: member.proposedBy,
	                    },
	                ],
	            };
	        });
	    }
	    collectOrderedKeys(perReviewer) {
	        const ordered = [];
	        const seen = new Set();
	        for (const { index } of perReviewer) {
	            for (const key of index.keys()) {
	                if (!seen.has(key)) {
	                    seen.add(key);
	                    ordered.push(key);
	                }
	            }
	        }
	        return ordered;
	    }
	    collectColumnUnion(rows) {
	        const ordered = [];
	        const seen = new Set();
	        for (const row of rows) {
	            for (const key of Object.keys(row)) {
	                if (!seen.has(key)) {
	                    seen.add(key);
	                    ordered.push(key);
	                }
	            }
	        }
	        return ordered;
	    }
	    pickRepresentative(rows) {
	        if (rows.length === 0) {
	            return undefined;
	        }
	        const grouped = new Map();
	        for (const row of rows) {
	            const serialized = this.stableSerialize(row);
	            const entry = grouped.get(serialized);
	            if (entry) {
	                entry.count += 1;
	            }
	            else {
	                grouped.set(serialized, { row, count: 1 });
	            }
	        }
	        return [...grouped.entries()].sort(([keyA, a], [keyB, b]) => b.count - a.count || keyA.localeCompare(keyB))[0][1]
	            .row;
	    }
	    stableSerialize(row) {
	        return JSON.stringify(Object.keys(row)
	            .sort()
	            .map((key) => [key, row[key]]));
	    }
	    tallyFieldCandidates(reviewersWithRow, rowKey, column, normalize) {
	        const groups = new Map();
	        for (const { reviewerId, index } of reviewersWithRow) {
	            const row = index.get(rowKey);
	            const original = row ? row[column] : undefined;
	            const normalized = normalize(column, original, row);
	            let group = groups.get(normalized);
	            if (!group) {
	                group = { value: original, normalizedValue: normalized, voteCount: 0, proposedBy: [] };
	                groups.set(normalized, group);
	            }
	            group.voteCount += 1;
	            group.proposedBy.push(reviewerId);
	        }
	        return [...groups.values()];
	    }
	}
	multiReviewerConsensus_service.MultiReviewerConsensusService = MultiReviewerConsensusService;
	
	return multiReviewerConsensus_service;
}

var sliceScopedLogger = {};

var hasRequiredSliceScopedLogger;

function requireSliceScopedLogger () {
	if (hasRequiredSliceScopedLogger) return sliceScopedLogger;
	hasRequiredSliceScopedLogger = 1;
	Object.defineProperty(sliceScopedLogger, "__esModule", { value: true });
	sliceScopedLogger.SliceScopedLogger = void 0;
	function isContextObject(value) {
	    return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Error);
	}
	class SliceScopedLogger {
	    constructor(sliceKey, delegate) {
	        this.sliceKey = sliceKey;
	        this.delegate = delegate;
	    }
	    info(message, ...meta) {
	        this.delegate.info(message, ...this.stamp(meta));
	    }
	    warn(message, ...meta) {
	        this.delegate.warn(message, ...this.stamp(meta));
	    }
	    error(message, ...meta) {
	        this.delegate.error(message, ...this.stamp(meta));
	    }
	    debug(message, ...meta) {
	        this.delegate.debug?.(message, ...this.stamp(meta));
	    }
	    verbose(message, ...meta) {
	        this.delegate.verbose?.(message, ...this.stamp(meta));
	    }
	    stamp(meta) {
	        const [first, ...rest] = meta;
	        if (isContextObject(first)) {
	            return [{ ...first, sliceKey: this.sliceKey }, ...rest];
	        }
	        return [...meta, { sliceKey: this.sliceKey }];
	    }
	}
	sliceScopedLogger.SliceScopedLogger = SliceScopedLogger;
	
	return sliceScopedLogger;
}

var signalAttributes_comparison = {};

var signalAttributesDenormalized = {};

var CadstromUid$1 = {};

var CadstromUid = {};

var CadstromObjectType = {};

var hasRequiredCadstromObjectType;

function requireCadstromObjectType () {
	if (hasRequiredCadstromObjectType) return CadstromObjectType;
	hasRequiredCadstromObjectType = 1;
	Object.defineProperty(CadstromObjectType, "__esModule", { value: true });
	CadstromObjectType.CadstromObjectTypeSchema = CadstromObjectType.CadstromObjectType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var CadstromObjectType$1;
	(function (CadstromObjectType) {
	    CadstromObjectType["BomItem"] = "bomitm";
	    CadstromObjectType["Build"] = "b";
	    CadstromObjectType["Boundary"] = "bnd";
	    CadstromObjectType["DifferentialPair"] = "diffpair";
	    CadstromObjectType["DocumentChunk"] = "dchunk";
	    CadstromObjectType["FileIndexEntry"] = "fi";
	    CadstromObjectType["InferenceHistory"] = "infhist";
	    CadstromObjectType["InferredDataEntry"] = "infentry";
	    CadstromObjectType["Manufacturer"] = "mfgr";
	    CadstromObjectType["ManufacturerAlias"] = "mfgralias";
	    CadstromObjectType["Membership"] = "mbr";
	    CadstromObjectType["Operation"] = "op";
	    CadstromObjectType["Organization"] = "org";
	    CadstromObjectType["PackageFunction"] = "func";
	    CadstromObjectType["PackageGroundTruthReview"] = "pkggtrev";
	    CadstromObjectType["PackageMode"] = "mode";
	    CadstromObjectType["PackageStorePackage"] = "pkg";
	    CadstromObjectType["PackageStorePackageFamily"] = "pkgfam";
	    CadstromObjectType["PackageStorePin"] = "pkgpin";
	    CadstromObjectType["PackageTag"] = "pkgtag";
	    CadstromObjectType["Project"] = "p";
	    CadstromObjectType["ProjectComponent"] = "prjcmp";
	    CadstromObjectType["ProjectModel"] = "prjmodel";
	    CadstromObjectType["ProjectModule"] = "prjmodule";
	    CadstromObjectType["ResolutionEntry"] = "resentry";
	    CadstromObjectType["Requirement"] = "req";
	    CadstromObjectType["Session"] = "sess";
	    CadstromObjectType["AgentChatConversation"] = "agconv";
	    CadstromObjectType["Signal"] = "sig";
	    CadstromObjectType["Tag"] = "tag";
	    CadstromObjectType["User"] = "u";
	    CadstromObjectType["Evidence"] = "evidence";
	    CadstromObjectType["Claim"] = "claim";
	    CadstromObjectType["Finding"] = "finding";
	    CadstromObjectType["DenormalizedPackageStorePackageSnapshot"] = "psnap";
	    CadstromObjectType["PackageBenchmarkDataset"] = "pbd";
	    CadstromObjectType["PackageBenchmarkDatasetSample"] = "pbds";
	    CadstromObjectType["Experiment"] = "exp";
	    CadstromObjectType["ExperimentSampleResult"] = "expsr";
	    CadstromObjectType["ExperimentScoring"] = "expscore";
	    CadstromObjectType["ResolutionScoringRun"] = "resrun";
	    CadstromObjectType["PackageEvaluation"] = "pkgeval";
	    CadstromObjectType["PackageGroundTruthAssertion"] = "pgta";
	    CadstromObjectType["PackageDataErrorStatement"] = "pdes";
	    CadstromObjectType["PackageDataAdjudicationItem"] = "pdai";
	    CadstromObjectType["PackageDataAdjudicationRequest"] = "pdar";
	    CadstromObjectType["FileEvidenceLocator"] = "fevl";
	    CadstromObjectType["EvidenceVote"] = "evvt";
	    CadstromObjectType["ConsensusRun"] = "consrun";
	    CadstromObjectType["WizardPlan"] = "wizplan";
	    CadstromObjectType["WizardStep"] = "wizstep";
	    CadstromObjectType["WizardInput"] = "wizinput";
	    CadstromObjectType["WizardOutput"] = "wizoutput";
	    CadstromObjectType["WizardFragment"] = "wizfrag";
	    CadstromObjectType["WizardFragmentLink"] = "wizlink";
	    CadstromObjectType["WizardReview"] = "wizrev";
	    CadstromObjectType["WizardReviewReplicaSet"] = "wizrevset";
	    CadstromObjectType["UserCapability"] = "usrcap";
	    CadstromObjectType["NetworkView"] = "netview";
	    CadstromObjectType["CircuitState"] = "cs";
	    CadstromObjectType["AssertionDefinition"] = "asrt";
	    CadstromObjectType["AssertionConfiguration"] = "asrtcfg";
	    CadstromObjectType["RequirementAssertionResult"] = "asrtres";
	    CadstromObjectType["RequirementLogEntry"] = "asrtlog";
	    CadstromObjectType["RequirementAttachment"] = "asrtatch";
	    CadstromObjectType["BuildDesignObject"] = "bdo";
	    CadstromObjectType["CustomSubject"] = "csubj";
	    CadstromObjectType["PackageDataSpecClause"] = "pdsc";
	    CadstromObjectType["PackageInferenceErrorCode"] = "piec";
	    CadstromObjectType["SignalMappingRun"] = "smrun";
	})(CadstromObjectType$1 || (CadstromObjectType.CadstromObjectType = CadstromObjectType$1 = {}));
	CadstromObjectType.CadstromObjectTypeSchema = zod_1.default.nativeEnum(CadstromObjectType$1);
	
	return CadstromObjectType;
}

var hasRequiredCadstromUid$1;

function requireCadstromUid$1 () {
	if (hasRequiredCadstromUid$1) return CadstromUid;
	hasRequiredCadstromUid$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AnyCadstromUidRegex = exports.CadstromUidTypes = exports.CADSTROM_UUID_SUFFIX_PLACEHOLDER = void 0;
		exports.isCadstromUidOfType = isCadstromUidOfType;
		exports.cadstromUidRegex = cadstromUidRegex;
		exports.generateCadstromUid = generateCadstromUid;
		exports.regenerateCadstromUid = regenerateCadstromUid;
		exports.regenerateRecordId = regenerateRecordId;
		exports.getObjectTypeDefinitionFromUid = getObjectTypeDefinitionFromUid;
		exports.cadstromUidSchema = cadstromUidSchema;
		exports.looksLikeCadstromUid = looksLikeCadstromUid;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const CadstromObjectType_1 = requireCadstromObjectType();
		exports.CADSTROM_UUID_SUFFIX_PLACEHOLDER = Array(32).fill('0').join('');
		exports.CadstromUidTypes = Object.values(CadstromObjectType_1.CadstromObjectType).reduce((acc, cur) => {
		    acc[cur] = {
		        name: cur,
		        prefix: cur,
		    };
		    return acc;
		}, {});
		exports.AnyCadstromUidRegex = new RegExp(`^(${Object.values(exports.CadstromUidTypes)
		    .map((t) => t.prefix)
		    .join('|')})_[a-zA-Z0-9]+$`);
		function isCadstromUidOfType(uid, type) {
		    return cadstromUidRegex(type).test(uid);
		}
		const regexCache = new Map();
		function cadstromUidRegex(objectType) {
		    if (!regexCache.has(objectType)) {
		        regexCache.set(objectType, new RegExp(`^${exports.CadstromUidTypes[objectType].prefix}_[a-zA-Z0-9]+$`));
		    }
		    return regexCache.get(objectType);
		}
		function generateCadstromUid(objectType, suffix, genMd5) {
		    const type = exports.CadstromUidTypes[objectType];
		    if (!type) {
		        throw new Error(`Invalid object type: ${objectType}`);
		    }
		    if (suffix) {
		        if (!genMd5) {
		            throw new Error(`genMd5 argument is required when providing a suffix`);
		        }
		        suffix = genMd5(suffix.toString()).substring(0, 32).toLowerCase();
		    }
		    else {
		        suffix = sigma_engine_shared_1.Utils.randomAlphaStr(36);
		    }
		    return `${type.prefix}_${suffix}`;
		}
		function regenerateCadstromUid(existingUid) {
		    const separatorIndex = existingUid.indexOf('_');
		    if (separatorIndex <= 0) {
		        throw new Error(`Cannot regenerate id from malformed Cadstrom UID: ${existingUid}`);
		    }
		    const prefix = existingUid.slice(0, separatorIndex);
		    return `${prefix}_${sigma_engine_shared_1.Utils.randomAlphaStr(36)}`;
		}
		function regenerateRecordId(existingId) {
		    return existingId.indexOf('_') > 0 ? regenerateCadstromUid(existingId) : sigma_engine_shared_1.Utils.randomAlphaStr(36);
		}
		function getObjectTypeDefinitionFromUid(uid) {
		    const [type, id] = uid?.split('_', 2) ?? [];
		    if (!type) {
		        throw new Error(`Invalid object id`);
		    }
		    if (!id || id.length < 32) {
		        throw new Error(`Invalid object id: ${id}. Must be at least 32 characters long`);
		    }
		    if (/[^a-zA-Z0-9]/.test(id)) {
		        throw new Error(`Invalid object id: ${id}. Must only contain alphanumeric characters`);
		    }
		    for (const definition of Object.values(exports.CadstromUidTypes)) {
		        if (definition.prefix === type) {
		            return definition;
		        }
		    }
		    throw new Error(`Invalid object type: ${type}`);
		}
		function cadstromUidSchema(type) {
		    return zod_1.z
		        .string()
		        .refine((value) => isCadstromUidOfType(value, type), { message: `Value must be a Cadstrom UID of type '${type}'` });
		}
		function looksLikeCadstromUid(value, objectType) {
		    try {
		        const definition = getObjectTypeDefinitionFromUid(value);
		        if (objectType !== undefined) {
		            return definition.name === objectType;
		        }
		        return true;
		    }
		    catch {
		        return false;
		    }
		}
		
	} (CadstromUid));
	return CadstromUid;
}

var globalAssetsSentinelOwnerId = {};

var hasRequiredGlobalAssetsSentinelOwnerId;

function requireGlobalAssetsSentinelOwnerId () {
	if (hasRequiredGlobalAssetsSentinelOwnerId) return globalAssetsSentinelOwnerId;
	hasRequiredGlobalAssetsSentinelOwnerId = 1;
	Object.defineProperty(globalAssetsSentinelOwnerId, "__esModule", { value: true });
	globalAssetsSentinelOwnerId.GLOBAL_ASSETS_SENTINEL_OWNER_ID = void 0;
	globalAssetsSentinelOwnerId.GLOBAL_ASSETS_SENTINEL_OWNER_ID = 'org_globalassets00000000000000000000';
	
	return globalAssetsSentinelOwnerId;
}

var fileConfidentiality = {};

var hasRequiredFileConfidentiality;

function requireFileConfidentiality () {
	if (hasRequiredFileConfidentiality) return fileConfidentiality;
	hasRequiredFileConfidentiality = 1;
	Object.defineProperty(fileConfidentiality, "__esModule", { value: true });
	fileConfidentiality.isConfidentialFileOwner = isConfidentialFileOwner;
	const global_assets_sentinel_owner_id_1 = requireGlobalAssetsSentinelOwnerId();
	function isConfidentialFileOwner(ownerId) {
	    return ownerId != null && ownerId !== global_assets_sentinel_owner_id_1.GLOBAL_ASSETS_SENTINEL_OWNER_ID;
	}
	
	return fileConfidentiality;
}

var hasRequiredCadstromUid;

function requireCadstromUid () {
	if (hasRequiredCadstromUid) return CadstromUid$1;
	hasRequiredCadstromUid = 1;
	(function (exports) {
		var __createBinding = (CadstromUid$1 && CadstromUid$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (CadstromUid$1 && CadstromUid$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireCadstromUid$1(), exports);
		__exportStar(requireCadstromObjectType(), exports);
		__exportStar(requireGlobalAssetsSentinelOwnerId(), exports);
		__exportStar(requireFileConfidentiality(), exports);
		
	} (CadstromUid$1));
	return CadstromUid$1;
}

var generateReadableId = {};

var hasRequiredGenerateReadableId;

function requireGenerateReadableId () {
	if (hasRequiredGenerateReadableId) return generateReadableId;
	hasRequiredGenerateReadableId = 1;
	Object.defineProperty(generateReadableId, "__esModule", { value: true });
	generateReadableId.generateReadableId = generateReadableId$1;
	generateReadableId.generateReadableConditionModeId = generateReadableConditionModeId;
	generateReadableId.generateSimplifiedIdsMap = generateSimplifiedIdsMap;
	const sigma_engine_shared_1 = requireDist$1();
	function generateReadableId$1(objectType, name, numRandomSuffixDigits = 4) {
	    const encodedName = name.toLowerCase().replace(/[^a-zA-Z0-9]/g, (match) => '_' + match.charCodeAt(0).toString(36));
	    const randomSuffix = numRandomSuffixDigits > 0 ? sigma_engine_shared_1.Utils.randomAlphaStr(numRandomSuffixDigits) : '';
	    return `${objectType}_${encodedName}${encodedName.length > 0 && numRandomSuffixDigits > 0 ? '__' : ''}${randomSuffix}`;
	}
	function formatValueForId(value) {
	    const absValue = Math.abs(value);
	    const formattedValue = (Number.isInteger(absValue) ? absValue.toFixed(1) : absValue.toString()).replace('.', 'p');
	    return value < 0 ? `n${formattedValue}` : formattedValue;
	}
	function generateReadableConditionModeId(signalName, minValue, nominalValue, maxValue) {
	    const normalizedSignalName = signalName.toLowerCase();
	    const normalizedMin = minValue !== null ? formatValueForId(minValue) : null;
	    const normalizedNominal = nominalValue !== null ? formatValueForId(nominalValue) : null;
	    const normalizedMax = maxValue !== null ? formatValueForId(maxValue) : null;
	    if (normalizedMin !== null && normalizedMax !== null) {
	        return `${normalizedSignalName}_${normalizedMin}_${normalizedMax}`;
	    }
	    else if (normalizedMin !== null) {
	        return `${normalizedSignalName}_above_${normalizedMin}`;
	    }
	    else if (normalizedMax !== null) {
	        return `${normalizedSignalName}_below_${normalizedMax}`;
	    }
	    else if (normalizedNominal !== null) {
	        return `${normalizedSignalName}_${normalizedNominal}`;
	    }
	    else {
	        throw new Error('At least one of minValue, nominalValue, or maxValue must be provided');
	    }
	}
	function generateSimplifiedIdsMap(originalIdsWithNames, objectType) {
	    const seenBaseIds = new Map();
	    return Object.fromEntries(originalIdsWithNames.map(({ id, name }) => {
	        const baseId = generateReadableId$1(objectType, name, 0);
	        const count = seenBaseIds.get(baseId) ?? 0;
	        const newId = count === 0 ? baseId : `${baseId}__${count}`;
	        seenBaseIds.set(baseId, count + 1);
	        return [id, newId];
	    }));
	}
	
	return generateReadableId;
}

var hasRequiredSignalAttributesDenormalized;

function requireSignalAttributesDenormalized () {
	if (hasRequiredSignalAttributesDenormalized) return signalAttributesDenormalized;
	hasRequiredSignalAttributesDenormalized = 1;
	Object.defineProperty(signalAttributesDenormalized, "__esModule", { value: true });
	signalAttributesDenormalized.SignalAttributesTerseSchema = signalAttributesDenormalized.SignalAttributesDenormalizedSchema = void 0;
	signalAttributesDenormalized.transformToTerseFormat = transformToTerseFormat;
	signalAttributesDenormalized.getSignalAttributesInDenormalizedFormat = getSignalAttributesInDenormalizedFormat;
	signalAttributesDenormalized.convertSignalAttributesToSignal = convertSignalAttributesToSignal;
	signalAttributesDenormalized.createMockSignalAttributes = createMockSignalAttributes;
	signalAttributesDenormalized.addSignalIdsToAttributes = addSignalIdsToAttributes;
	const zod_1 = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	const CadstromUid_1 = requireCadstromUid();
	const generate_readable_id_1 = requireGenerateReadableId();
	signalAttributesDenormalized.SignalAttributesDenormalizedSchema = zod_1.z.object({
	    signalName: zod_1.z.string(),
	    power: zod_1.z.boolean(),
	    ground: zod_1.z.boolean(),
	    analog: zod_1.z.boolean(),
	    digital: zod_1.z.boolean(),
	    exposedPad: zod_1.z.boolean(),
	    noConnect: zod_1.z.boolean(),
	    activeLow: zod_1.z.boolean(),
	    direction: zod_1.z.nativeEnum(sigma_engine_shared_1.DesignModels.SignalDirection).nullable(),
	    description: zod_1.z.string().nullable(),
	});
	signalAttributesDenormalized.SignalAttributesTerseSchema = zod_1.z.object({
	    signalName: zod_1.z.string(),
	    description: zod_1.z.string().optional(),
	    attributes: zod_1.z.array(zod_1.z.string()),
	    direction: zod_1.z.nativeEnum(sigma_engine_shared_1.DesignModels.SignalDirection).optional(),
	});
	function transformToTerseFormat(denormalized) {
	    const attributes = [];
	    for (const [key, value] of Object.entries(denormalized)) {
	        if (typeof value === 'boolean' && value === true) {
	            attributes.push(key);
	        }
	    }
	    return {
	        signalName: denormalized.signalName,
	        attributes,
	        ...(denormalized.description !== null && { description: denormalized.description }),
	        ...(denormalized.direction !== null && { direction: denormalized.direction }),
	    };
	}
	function getSignalAttributesInDenormalizedFormat(signal) {
	    return {
	        signalName: signal.name,
	        power: signal.attributes?.power ?? false,
	        ground: signal.attributes?.ground ?? false,
	        analog: signal.attributes?.analog ?? false,
	        digital: signal.attributes?.digital ?? false,
	        exposedPad: signal.attributes?.exposedPad ?? false,
	        noConnect: signal.attributes?.noConnect ?? false,
	        activeLow: signal.attributes?.activeLow ?? false,
	        direction: signal.signalDirection ?? null,
	        description: signal.description ?? null,
	    };
	}
	function convertSignalAttributesToSignal(attributes, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    const signal = {
	        id: attributes.id,
	        name: attributes.signalName,
	        padRefs: [],
	        pinRefs: [],
	        signalDirection: attributes.direction ?? undefined,
	        attributes: {
	            power: attributes.power,
	            ground: attributes.ground,
	            analog: attributes.analog,
	            digital: attributes.digital,
	            exposedPad: attributes.exposedPad,
	            noConnect: attributes.noConnect,
	            activeLow: attributes.activeLow,
	        },
	        description: attributes.description ?? undefined,
	    };
	    const parseResult = sigma_engine_shared_1.DesignModels.SignalSchema.safeParse(signal);
	    if (!parseResult.success) {
	        logger.warn('ConvertSignalAttributesToSignal_InvalidFieldsDropped', {
	            signalId: attributes.id,
	            signalName: attributes.signalName,
	            errors: parseResult.error.flatten().fieldErrors,
	        });
	        return {
	            id: attributes.id,
	            name: attributes.signalName,
	            padRefs: [],
	            pinRefs: [],
	        };
	    }
	    return parseResult.data;
	}
	function createMockSignalAttributes(overrides = {}) {
	    return {
	        signalName: 'TEST',
	        power: false,
	        ground: false,
	        analog: false,
	        digital: false,
	        exposedPad: false,
	        noConnect: false,
	        activeLow: false,
	        direction: sigma_engine_shared_1.DesignModels.SignalDirection.Input,
	        description: 'Test signal',
	        ...overrides,
	    };
	}
	function addSignalIdsToAttributes(attributes) {
	    const signalAttributesWithIds = attributes.map((signalAttribute) => ({
	        ...signalAttribute,
	        id: (0, generate_readable_id_1.generateReadableId)(CadstromUid_1.CadstromObjectType.Signal, signalAttribute.signalName),
	    }));
	    return signalAttributesWithIds;
	}
	
	return signalAttributesDenormalized;
}

var hasRequiredSignalAttributes_comparison;

function requireSignalAttributes_comparison () {
	if (hasRequiredSignalAttributes_comparison) return signalAttributes_comparison;
	hasRequiredSignalAttributes_comparison = 1;
	Object.defineProperty(signalAttributes_comparison, "__esModule", { value: true });
	signalAttributes_comparison.SignalAttributesComparison = signalAttributes_comparison.SignalReviewAttributesRowSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const signal_attributes_denormalized_1 = requireSignalAttributesDenormalized();
	const auto_resolve_policy_1 = requireAutoResolvePolicy();
	const comparison_spec_1 = requireComparisonSpec();
	signalAttributes_comparison.SignalReviewAttributesRowSchema = signal_attributes_denormalized_1.SignalAttributesDenormalizedSchema.extend({
	    modelSignal: z
	        .string()
	        .nullish()
	        .transform((value) => value ?? null),
	});
	const VALUE_COLUMNS = [
	    'power',
	    'ground',
	    'analog',
	    'digital',
	    'exposedPad',
	    'noConnect',
	    'activeLow',
	    'direction',
	    'description',
	    'modelSignal',
	];
	class SignalAttributesComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(['signalName'], VALUE_COLUMNS);
	        this.membershipScopeColumns = ['signalName'];
	        this.autoResolveColumns = new Map([['description', new auto_resolve_policy_1.PluralityPreferNonEmptyAutoResolvePolicy()]]);
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value) => this.normalize(column, value));
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    normalize(column, value) {
	        if (column === 'signalName' && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        return super.normalize(column, value);
	    }
	}
	signalAttributes_comparison.SignalAttributesComparison = SignalAttributesComparison;
	
	return signalAttributes_comparison;
}

var driveModes_comparison = {};

var hasRequiredDriveModes_comparison;

function requireDriveModes_comparison () {
	if (hasRequiredDriveModes_comparison) return driveModes_comparison;
	hasRequiredDriveModes_comparison = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DriveModesComparison = exports.NEVER_REVIEWED_DRIVE_MODE_COLUMNS = exports.DRIVE_MODE_VALUE_COLUMNS = exports.DRIVE_MODE_KEY_COLUMNS = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const value_comparison_1 = requireValueComparison();
		const si_comparison_1 = requireSiComparison();
		const signal_name_normalization_1 = requireSignalNameNormalization();
		const comparison_spec_1 = requireComparisonSpec();
		exports.DRIVE_MODE_KEY_COLUMNS = ['signalName', 'inputType', 'outputType', 'conditionId'];
		exports.DRIVE_MODE_VALUE_COLUMNS = [
		    'outputHighVoltage',
		    'outputLowVoltage',
		    'inputHighVoltage',
		    'inputLowVoltage',
		];
		exports.NEVER_REVIEWED_DRIVE_MODE_COLUMNS = [
		    'internalPullupBiasImpedance',
		    'internalPullupBiasVoltageReference',
		    'internalPullupBiasSignalReference',
		    'internalPulldownBiasImpedance',
		    'internalPulldownBiasVoltageReference',
		    'internalPulldownBiasSignalReference',
		    'outputImpedance',
		];
		const reviewedDriveModeColumns = exports.DRIVE_MODE_VALUE_COLUMNS;
		const overlappingDriveModeColumns = exports.NEVER_REVIEWED_DRIVE_MODE_COLUMNS.filter((column) => reviewedDriveModeColumns.includes(column));
		if (overlappingDriveModeColumns.length > 0) {
		    throw new Error(`Drive-mode column is both reviewed and never-reviewed: ${overlappingDriveModeColumns.join(', ')}`);
		}
		const SIGNAL_NAME_COLUMNS = new Set(['signalName']);
		const SI_COLUMN_CHARACTERISTICS = new Map([
		    ['outputHighVoltage', sigma_engine_shared_1.BoundaryCharacteristic.Voltage],
		    ['outputLowVoltage', sigma_engine_shared_1.BoundaryCharacteristic.Voltage],
		    ['inputHighVoltage', sigma_engine_shared_1.BoundaryCharacteristic.Voltage],
		    ['inputLowVoltage', sigma_engine_shared_1.BoundaryCharacteristic.Voltage],
		    ['internalPullupBiasVoltageReference', sigma_engine_shared_1.BoundaryCharacteristic.Voltage],
		    ['internalPulldownBiasVoltageReference', sigma_engine_shared_1.BoundaryCharacteristic.Voltage],
		    ['internalPullupBiasImpedance', sigma_engine_shared_1.BoundaryCharacteristic.Resistance],
		    ['internalPulldownBiasImpedance', sigma_engine_shared_1.BoundaryCharacteristic.Resistance],
		    ['outputImpedance', sigma_engine_shared_1.BoundaryCharacteristic.Resistance],
		]);
		class DriveModesComparison extends comparison_spec_1.ComparisonSpec {
		    constructor() {
		        super(...arguments);
		        this.alignment = new comparison_spec_1.CellAlignment(exports.DRIVE_MODE_KEY_COLUMNS, exports.DRIVE_MODE_VALUE_COLUMNS);
		        this.membershipScopeColumns = ['signalName'];
		    }
		    toRows(data) {
		        return data.map(({ signalId, ...rest }) => ({ ...rest, signalName: signalId }));
		    }
		    fromRows(rows) {
		        return rows.map(({ signalName, ...rest }) => ({ ...rest, signalId: signalName }));
		    }
		    rowKeyOf(row) {
		        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
		    }
		    normalize(column, value, row) {
		        if (SIGNAL_NAME_COLUMNS.has(column) && typeof value === 'string') {
		            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
		        }
		        const characteristic = SI_COLUMN_CHARACTERISTICS.get(column);
		        if (characteristic !== undefined && typeof value === 'string') {
		            return (0, si_comparison_1.formatSIOrSymbolicValue)(value, characteristic);
		        }
		        return super.normalize(column, value, row);
		    }
		}
		exports.DriveModesComparison = DriveModesComparison;
		
	} (driveModes_comparison));
	return driveModes_comparison;
}

var sigmaModelParameters_comparison = {};

var hasRequiredSigmaModelParameters_comparison;

function requireSigmaModelParameters_comparison () {
	if (hasRequiredSigmaModelParameters_comparison) return sigmaModelParameters_comparison;
	hasRequiredSigmaModelParameters_comparison = 1;
	Object.defineProperty(sigmaModelParameters_comparison, "__esModule", { value: true });
	sigmaModelParameters_comparison.SigmaModelParametersComparison = void 0;
	const si_comparison_1 = requireSiComparison();
	const value_comparison_1 = requireValueComparison();
	const comparison_spec_1 = requireComparisonSpec();
	class SigmaModelParametersComparison extends comparison_spec_1.ComparisonSpec {
	    constructor(aliases = {}, paramUnits = {}) {
	        super();
	        this.alignment = new comparison_spec_1.CellAlignment(['paramName'], ['value', 'variance', 'notSpecified']);
	        this.normalizedAliases = new Map(Object.entries(aliases).map(([alias, canonical]) => [alias.trim().toLowerCase(), canonical.trim().toLowerCase()]));
	        this.canonicalDisplay = new Map(Object.values(aliases).map((canonical) => [canonical.trim().toLowerCase(), canonical.trim()]));
	        this.paramUnits = new Map(Object.entries(paramUnits).map(([name, declaration]) => [name.trim().toLowerCase(), declaration]));
	    }
	    toRows(data) {
	        return Object.entries(data).map(([paramName, pair]) => ({
	            paramName,
	            value: pair.value,
	            variance: pair.variance,
	            ...(pair.notSpecified === true ? { notSpecified: true } : {}),
	        }));
	    }
	    fromRows(rows) {
	        const result = {};
	        for (const row of rows) {
	            result[row.paramName] = {
	                value: row.value ?? null,
	                variance: row.variance ?? null,
	                ...(row.notSpecified === true ? { notSpecified: true } : {}),
	            };
	        }
	        return result;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'paramName') {
	            return this.resolveParamName(value);
	        }
	        if (column === 'value' && typeof value === 'string') {
	            const declaration = this.unitsFor(row);
	            return declaration !== undefined ?
	                (0, si_comparison_1.formatSIOrSymbolicValueWithDeclaration)(value, declaration)
	                : super.normalize(column, value, row);
	        }
	        if (column === 'variance' && typeof value === 'string') {
	            return (0, si_comparison_1.formatVariance)(value);
	        }
	        return super.normalize(column, value, row);
	    }
	    canonicalizeKeys(row) {
	        const resolved = this.resolveParamName(row.paramName);
	        return { ...row, paramName: this.canonicalDisplay.get(resolved) ?? row.paramName };
	    }
	    unitsFor(row) {
	        return row?.paramName === undefined ? undefined : this.paramUnits.get(this.resolveParamName(row.paramName));
	    }
	    resolveParamName(value) {
	        const raw = typeof value === 'string' ? value.trim() : super.normalize('paramName', value);
	        const lowered = raw.toLowerCase();
	        return this.normalizedAliases.get(lowered) ?? lowered;
	    }
	}
	sigmaModelParameters_comparison.SigmaModelParametersComparison = SigmaModelParametersComparison;
	
	return sigmaModelParameters_comparison;
}

var pinout_comparison = {};

var pinIdNormalization = {};

var hasRequiredPinIdNormalization;

function requirePinIdNormalization () {
	if (hasRequiredPinIdNormalization) return pinIdNormalization;
	hasRequiredPinIdNormalization = 1;
	Object.defineProperty(pinIdNormalization, "__esModule", { value: true });
	pinIdNormalization.normalizePinIdForComparison = normalizePinIdForComparison;
	function normalizePinIdForComparison(raw) {
	    const trimmed = raw.trim();
	    const match = /^0*(\d+)(?:\.0+)?$/.exec(trimmed);
	    return match?.[1] ?? trimmed;
	}
	
	return pinIdNormalization;
}

var hasRequiredPinout_comparison;

function requirePinout_comparison () {
	if (hasRequiredPinout_comparison) return pinout_comparison;
	hasRequiredPinout_comparison = 1;
	Object.defineProperty(pinout_comparison, "__esModule", { value: true });
	pinout_comparison.PinoutComparison = pinout_comparison.SignalPinsRowSchema = void 0;
	pinout_comparison.splitPinList = splitPinList;
	pinout_comparison.joinPinList = joinPinList;
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const comparison_spec_1 = requireComparisonSpec();
	const pin_id_normalization_1 = requirePinIdNormalization();
	pinout_comparison.SignalPinsRowSchema = z.object({
	    signalName: z.string(),
	    pins: z.string(),
	});
	function comparePinsNaturally(a, b) {
	    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
	}
	function canonicalPins(pins) {
	    return pins.map((pin) => (0, pin_id_normalization_1.normalizePinIdForComparison)(pin)).sort(comparePinsNaturally);
	}
	function splitPinList(pins) {
	    return canonicalPins(pins
	        .split(/[\s,]+/)
	        .map((entry) => entry.trim())
	        .filter((entry) => entry !== ''));
	}
	function joinPinList(pins) {
	    return canonicalPins(pins).join(', ');
	}
	class PinoutComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(['signalName'], ['pins']);
	        this.membershipScopeColumns = ['signalName'];
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'signalName' && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        if (column === 'pins' && typeof value === 'string') {
	            return JSON.stringify(splitPinList(value));
	        }
	        return super.normalize(column, value, row);
	    }
	}
	pinout_comparison.PinoutComparison = PinoutComparison;
	
	return pinout_comparison;
}

var pinAssignments_comparison = {};

var signalNameCanonicalization = {};

var normalizeSignalName = {};

var hasRequiredNormalizeSignalName;

function requireNormalizeSignalName () {
	if (hasRequiredNormalizeSignalName) return normalizeSignalName;
	hasRequiredNormalizeSignalName = 1;
	Object.defineProperty(normalizeSignalName, "__esModule", { value: true });
	normalizeSignalName.normalizeSignalName = normalizeSignalName$1;
	const MINUS_LIKE = /[−–—]/g;
	const FULLWIDTH_PLUS = /＋/g;
	const FOOTNOTE_MARKERS = /[*†‡§¶⁰¹²³⁴⁵⁶⁷⁸⁹]/g;
	const PUNCTUATION_PERIOD = /(?<!\d)\.(?!\d)/g;
	const SLASH_SPACES = /\s*\/\s*/g;
	function normalizeSignalName$1(name) {
	    if (typeof name !== 'string') {
	        return name;
	    }
	    const stripped = name
	        .replace(MINUS_LIKE, '-')
	        .replace(FULLWIDTH_PLUS, '+')
	        .replace(FOOTNOTE_MARKERS, '')
	        .replace(PUNCTUATION_PERIOD, '')
	        .replace(SLASH_SPACES, '/')
	        .trim();
	    const signShifted = stripped.length > 1 && (stripped[0] === '+' || stripped[0] === '-') ?
	        `${stripped.slice(1)}${stripped[0]}`
	        : stripped;
	    return signShifted.trim();
	}
	
	return normalizeSignalName;
}

var hasRequiredSignalNameCanonicalization;

function requireSignalNameCanonicalization () {
	if (hasRequiredSignalNameCanonicalization) return signalNameCanonicalization;
	hasRequiredSignalNameCanonicalization = 1;
	Object.defineProperty(signalNameCanonicalization, "__esModule", { value: true });
	signalNameCanonicalization.canonicalizeSignalNameForComparison = canonicalizeSignalNameForComparison;
	const normalize_signal_name_1 = requireNormalizeSignalName();
	const OVERLINE_WRAPPER = /\\overline\{([^}]*)\}/g;
	const OVERLINE_COMBINING = /[̅‾̄]/g;
	const CANONICAL_OVERLINE_MARKER = '̅';
	function parseOverline(name) {
	    const hadWrapper = OVERLINE_WRAPPER.test(name);
	    OVERLINE_WRAPPER.lastIndex = 0;
	    const withoutWrapper = name.replace(OVERLINE_WRAPPER, '$1');
	    const hadCombining = OVERLINE_COMBINING.test(withoutWrapper);
	    OVERLINE_COMBINING.lastIndex = 0;
	    return { base: withoutWrapper.replace(OVERLINE_COMBINING, ''), hadOverline: hadWrapper || hadCombining };
	}
	function canonicalizeSignalNameForComparison(name) {
	    const { base, hadOverline } = parseOverline(name);
	    const normalized = (0, normalize_signal_name_1.normalizeSignalName)(base).toLowerCase();
	    return hadOverline ? `${normalized}${CANONICAL_OVERLINE_MARKER}` : normalized;
	}
	
	return signalNameCanonicalization;
}

var hasRequiredPinAssignments_comparison;

function requirePinAssignments_comparison () {
	if (hasRequiredPinAssignments_comparison) return pinAssignments_comparison;
	hasRequiredPinAssignments_comparison = 1;
	Object.defineProperty(pinAssignments_comparison, "__esModule", { value: true });
	pinAssignments_comparison.PinAssignmentsComparison = pinAssignments_comparison.PinAssignmentsRowSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const signal_name_canonicalization_1 = requireSignalNameCanonicalization();
	const value_comparison_1 = requireValueComparison();
	const comparison_spec_1 = requireComparisonSpec();
	const pin_id_normalization_1 = requirePinIdNormalization();
	pinAssignments_comparison.PinAssignmentsRowSchema = z.object({
	    pinId: z.string(),
	    signalName: z.string(),
	});
	class PinAssignmentsComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(['pinId'], ['signalName']);
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'pinId' && typeof value === 'string') {
	            return (0, pin_id_normalization_1.normalizePinIdForComparison)(value);
	        }
	        if (column === 'signalName' && typeof value === 'string') {
	            return (0, signal_name_canonicalization_1.canonicalizeSignalNameForComparison)(value);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	pinAssignments_comparison.PinAssignmentsComparison = PinAssignmentsComparison;
	
	return pinAssignments_comparison;
}

var scalar_comparison = {};

var hasRequiredScalar_comparison;

function requireScalar_comparison () {
	if (hasRequiredScalar_comparison) return scalar_comparison;
	hasRequiredScalar_comparison = 1;
	Object.defineProperty(scalar_comparison, "__esModule", { value: true });
	scalar_comparison.ScalarComparison = void 0;
	const value_comparison_1 = requireValueComparison();
	const comparison_spec_1 = requireComparisonSpec();
	class ScalarComparison extends comparison_spec_1.ComparisonSpec {
	    constructor(key, normalizeValue) {
	        super();
	        this.key = key;
	        this.normalizeValue = normalizeValue;
	        this.alignment = new comparison_spec_1.CellAlignment(['key'], ['value']);
	        this.membershipContestable = false;
	    }
	    toRows(data) {
	        return [{ key: this.key, value: data }];
	    }
	    fromRows(rows) {
	        const [row] = rows;
	        if (row === undefined) {
	            throw new Error(`Cannot reconstruct scalar '${this.key}' from zero rows.`);
	        }
	        return row.value;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'value' && this.normalizeValue !== undefined) {
	            return this.normalizeValue(value);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	scalar_comparison.ScalarComparison = ScalarComparison;
	
	return scalar_comparison;
}

var formStructure_comparison = {};

var hasRequiredFormStructure_comparison;

function requireFormStructure_comparison () {
	if (hasRequiredFormStructure_comparison) return formStructure_comparison;
	hasRequiredFormStructure_comparison = 1;
	Object.defineProperty(formStructure_comparison, "__esModule", { value: true });
	formStructure_comparison.FormStructureComparison = formStructure_comparison.FormStructureInstanceKind = void 0;
	const comparison_spec_1 = requireComparisonSpec();
	var FormStructureInstanceKind;
	(function (FormStructureInstanceKind) {
	    FormStructureInstanceKind["BlockInstance"] = "block_instance";
	    FormStructureInstanceKind["ArrayElement"] = "array_element";
	})(FormStructureInstanceKind || (formStructure_comparison.FormStructureInstanceKind = FormStructureInstanceKind = {}));
	class FormStructureComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.SetAlignment();
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return this.alignment.identityOf(row, (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	}
	formStructure_comparison.FormStructureComparison = FormStructureComparison;
	
	return formStructure_comparison;
}

var formSignalMapping_comparison = {};

var hasRequiredFormSignalMapping_comparison;

function requireFormSignalMapping_comparison () {
	if (hasRequiredFormSignalMapping_comparison) return formSignalMapping_comparison;
	hasRequiredFormSignalMapping_comparison = 1;
	Object.defineProperty(formSignalMapping_comparison, "__esModule", { value: true });
	formSignalMapping_comparison.FormSignalMappingComparison = formSignalMapping_comparison.FormSignalMappingRowSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const comparison_spec_1 = requireComparisonSpec();
	const pin_id_normalization_1 = requirePinIdNormalization();
	formSignalMapping_comparison.FormSignalMappingRowSchema = z.object({
	    modelSignalRef: z.string(),
	    blockName: z.string(),
	    blockIndex: z.number(),
	    signalName: z.string(),
	    pins: z.string().array(),
	    floatingBehavior: sigma_engine_shared_1.DesignModels.SignalFloatingBehaviorSchema.optional(),
	});
	class FormSignalMappingComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(['modelSignalRef', 'blockName', 'blockIndex'], ['signalName', 'pins', 'floatingBehavior']);
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'signalName' && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        if (column === 'pins' && Array.isArray(value)) {
	            const canonical = value
	                .map((pin) => (typeof pin === 'string' ? (0, pin_id_normalization_1.normalizePinIdForComparison)(pin) : String(pin)))
	                .sort();
	            return JSON.stringify(canonical);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	formSignalMapping_comparison.FormSignalMappingComparison = FormSignalMappingComparison;
	
	return formSignalMapping_comparison;
}

var functionSignalMapping_comparison = {};

var hasRequiredFunctionSignalMapping_comparison;

function requireFunctionSignalMapping_comparison () {
	if (hasRequiredFunctionSignalMapping_comparison) return functionSignalMapping_comparison;
	hasRequiredFunctionSignalMapping_comparison = 1;
	Object.defineProperty(functionSignalMapping_comparison, "__esModule", { value: true });
	functionSignalMapping_comparison.FunctionSignalMappingComparison = functionSignalMapping_comparison.FunctionSignalMappingRowSchema = void 0;
	functionSignalMapping_comparison.functionSignalMappingRowKey = functionSignalMappingRowKey;
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const comparison_spec_1 = requireComparisonSpec();
	functionSignalMapping_comparison.FunctionSignalMappingRowSchema = z.object({
	    functionName: z.string(),
	    functionSignalName: z.string(),
	    signalNames: z.string().array(),
	});
	function functionSignalMappingRowKey(functionName, functionSignalName) {
	    return [(0, value_comparison_1.normalizeForComparison)(functionName), (0, signal_name_normalization_1.normalizeSignalNameForComparison)(functionSignalName)].join('\x1F');
	}
	class FunctionSignalMappingComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(['functionName', 'functionSignalName'], ['signalNames']);
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'functionSignalName' && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        if (column === 'signalNames' && Array.isArray(value)) {
	            const canonical = value
	                .map((name) => (typeof name === 'string' ? (0, signal_name_normalization_1.normalizeSignalNameForComparison)(name) : String(name)))
	                .sort();
	            return JSON.stringify(canonical);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	functionSignalMapping_comparison.FunctionSignalMappingComparison = FunctionSignalMappingComparison;
	
	return functionSignalMapping_comparison;
}

var ncPins_comparison = {};

var hasRequiredNcPins_comparison;

function requireNcPins_comparison () {
	if (hasRequiredNcPins_comparison) return ncPins_comparison;
	hasRequiredNcPins_comparison = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NcPinsComparison = exports.NcPinsRowSchema = exports.NC_PINS_ROW_KEY = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const z = /*@__PURE__*/ requireZod();
		const value_comparison_1 = requireValueComparison();
		const comparison_spec_1 = requireComparisonSpec();
		const pin_id_normalization_1 = requirePinIdNormalization();
		exports.NC_PINS_ROW_KEY = 'nc_pins';
		exports.NcPinsRowSchema = z.object({
		    key: z.string(),
		    pins: z.string().array(),
		    floatingBehavior: sigma_engine_shared_1.DesignModels.SignalFloatingBehaviorSchema.optional(),
		});
		class NcPinsComparison extends comparison_spec_1.ComparisonSpec {
		    constructor() {
		        super(...arguments);
		        this.alignment = new comparison_spec_1.CellAlignment(['key'], ['pins', 'floatingBehavior']);
		        this.membershipContestable = false;
		    }
		    toRows(data) {
		        return [{ key: exports.NC_PINS_ROW_KEY, ...data }];
		    }
		    fromRows(rows) {
		        const [row] = rows;
		        if (row === undefined) {
		            return { pins: [] };
		        }
		        const { key: _key, ...data } = row;
		        return data;
		    }
		    rowKeyOf(row) {
		        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
		    }
		    normalize(column, value, row) {
		        if (column === 'pins' && Array.isArray(value)) {
		            const canonical = value
		                .map((pin) => (typeof pin === 'string' ? (0, pin_id_normalization_1.normalizePinIdForComparison)(pin) : String(pin)))
		                .sort();
		            return JSON.stringify(canonical);
		        }
		        return super.normalize(column, value, row);
		    }
		}
		exports.NcPinsComparison = NcPinsComparison;
		
	} (ncPins_comparison));
	return ncPins_comparison;
}

var signalSet_comparison = {};

var hasRequiredSignalSet_comparison;

function requireSignalSet_comparison () {
	if (hasRequiredSignalSet_comparison) return signalSet_comparison;
	hasRequiredSignalSet_comparison = 1;
	Object.defineProperty(signalSet_comparison, "__esModule", { value: true });
	signalSet_comparison.SignalSetComparison = signalSet_comparison.SignalSetRowSchema = void 0;
	signalSet_comparison.signalSetRowKeyForName = signalSetRowKeyForName;
	const z = /*@__PURE__*/ requireZod();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const comparison_spec_1 = requireComparisonSpec();
	signalSet_comparison.SignalSetRowSchema = z.object({
	    signalId: z.string(),
	    signalName: z.string(),
	    pins: z.string().array(),
	    attributes: z.string(),
	    description: z.string(),
	});
	const IDENTITY_COLUMNS = ['signalName'];
	class SignalNameIdentityAlignment extends comparison_spec_1.SetAlignment {
	    identityOf(row, normalize) {
	        return JSON.stringify(IDENTITY_COLUMNS.map((column) => [column, normalize(column, row[column], row)]));
	    }
	}
	function signalSetRowKeyForName(signalName) {
	    return JSON.stringify([['signalName', (0, signal_name_normalization_1.normalizeSignalNameForComparison)(signalName)]]);
	}
	class SignalSetComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new SignalNameIdentityAlignment();
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return this.alignment.identityOf(row, (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (column === 'signalName' && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	signalSet_comparison.SignalSetComparison = SignalSetComparison;
	
	return signalSet_comparison;
}

var boundaryConditions_comparison = {};

var hasRequiredBoundaryConditions_comparison;

function requireBoundaryConditions_comparison () {
	if (hasRequiredBoundaryConditions_comparison) return boundaryConditions_comparison;
	hasRequiredBoundaryConditions_comparison = 1;
	Object.defineProperty(boundaryConditions_comparison, "__esModule", { value: true });
	boundaryConditions_comparison.BoundaryConditionsComparison = boundaryConditions_comparison.BoundaryConditionRowSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	const si_comparison_1 = requireSiComparison();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const comparison_spec_1 = requireComparisonSpec();
	boundaryConditions_comparison.BoundaryConditionRowSchema = z.object({
	    signalName: z.string(),
	    relativeToSignalName: z.string(),
	    characteristic: z.string(),
	    conditions: z.string(),
	    minimum: z.string().nullable(),
	    nominal: z.string().nullable(),
	    maximum: z.string().nullable(),
	    sourceConditions: z.unknown().optional(),
	});
	const KEY_COLUMNS = [
	    'signalName',
	    'relativeToSignalName',
	    'characteristic',
	    'conditions',
	];
	const VALUE_COLUMNS = ['minimum', 'nominal', 'maximum'];
	const SI_VALUE_COLUMNS = new Set(['minimum', 'nominal', 'maximum']);
	const SIGNAL_NAME_COLUMNS = new Set(['signalName', 'relativeToSignalName']);
	class BoundaryConditionsComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(KEY_COLUMNS, VALUE_COLUMNS);
	        this.membershipScopeColumns = ['signalName'];
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if (SIGNAL_NAME_COLUMNS.has(column) && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        if (SI_VALUE_COLUMNS.has(column) && typeof value === 'string') {
	            const characteristic = sigma_engine_shared_1.BoundaryCharacteristicSchema.safeParse(row?.characteristic);
	            if (characteristic.success) {
	                return (0, si_comparison_1.formatSIOrSymbolicValue)(value, characteristic.data);
	            }
	        }
	        return super.normalize(column, value, row);
	    }
	}
	boundaryConditions_comparison.BoundaryConditionsComparison = BoundaryConditionsComparison;
	
	return boundaryConditions_comparison;
}

var floatingBehaviour_comparison = {};

var hasRequiredFloatingBehaviour_comparison;

function requireFloatingBehaviour_comparison () {
	if (hasRequiredFloatingBehaviour_comparison) return floatingBehaviour_comparison;
	hasRequiredFloatingBehaviour_comparison = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FloatingBehaviourComparison = exports.FloatingBehaviourRowSchema = exports.FloatingBehaviourValueSchema = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const z = /*@__PURE__*/ requireZod();
		const value_comparison_1 = requireValueComparison();
		const signal_name_normalization_1 = requireSignalNameNormalization();
		const comparison_spec_1 = requireComparisonSpec();
		exports.FloatingBehaviourValueSchema = z.union([sigma_engine_shared_1.SignalFloatingBehaviorSchema, z.literal('')]);
		exports.FloatingBehaviourRowSchema = z.object({
		    signalName: z.string(),
		    floatingBehaviour: exports.FloatingBehaviourValueSchema,
		});
		class FloatingBehaviourComparison extends comparison_spec_1.ComparisonSpec {
		    constructor() {
		        super(...arguments);
		        this.alignment = new comparison_spec_1.CellAlignment(['signalName'], ['floatingBehaviour']);
		        this.membershipScopeColumns = ['signalName'];
		    }
		    toRows(data) {
		        return data;
		    }
		    fromRows(rows) {
		        return rows;
		    }
		    rowKeyOf(row) {
		        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
		    }
		    normalize(column, value, row) {
		        if (column === 'signalName' && typeof value === 'string') {
		            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
		        }
		        return super.normalize(column, value, row);
		    }
		    admitsValue(column, value) {
		        return column !== 'floatingBehaviour' || value === null || exports.FloatingBehaviourValueSchema.safeParse(value).success;
		    }
		}
		exports.FloatingBehaviourComparison = FloatingBehaviourComparison;
		
	} (floatingBehaviour_comparison));
	return floatingBehaviour_comparison;
}

var signalTies_comparison = {};

var SignalTies = {};

var signalTieTypes = {};

var hasRequiredSignalTieTypes;

function requireSignalTieTypes () {
	if (hasRequiredSignalTieTypes) return signalTieTypes;
	hasRequiredSignalTieTypes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TIE_COMPONENT_TYPE_TO_UNITS = exports.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC = exports.TIE_COMPONENT_TYPE_TO_MODEL = exports.MODEL_TO_TIE_COMPONENT_TYPE = exports.flatSignalTieSchema = exports.flatSignalTieComponentValueSchema = exports.flatSignalTieComponentValueRangeSchema = exports.flatSignalTieComponentValueAbsoluteSchema = exports.SignalTiePolarity = exports.SignalTieVarianceType = exports.SignalTieComponentType = exports.SignalTieConnectionType = void 0;
		exports.isPolarizedTieComponentType = isPolarizedTieComponentType;
		exports.reverseSignalTiePolarity = reverseSignalTiePolarity;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		var SignalTieConnectionType;
		(function (SignalTieConnectionType) {
		    SignalTieConnectionType["Direct"] = "direct";
		    SignalTieConnectionType["ThroughComponent"] = "throughComponent";
		})(SignalTieConnectionType || (exports.SignalTieConnectionType = SignalTieConnectionType = {}));
		var SignalTieComponentType;
		(function (SignalTieComponentType) {
		    SignalTieComponentType["Capacitor"] = "capacitor";
		    SignalTieComponentType["CapacitorPolarized"] = "capacitorPolarized";
		    SignalTieComponentType["Inductor"] = "inductor";
		    SignalTieComponentType["Resistor"] = "resistor";
		})(SignalTieComponentType || (exports.SignalTieComponentType = SignalTieComponentType = {}));
		var SignalTieVarianceType;
		(function (SignalTieVarianceType) {
		    SignalTieVarianceType["Absolute"] = "absolute";
		    SignalTieVarianceType["Relative"] = "relative";
		})(SignalTieVarianceType || (exports.SignalTieVarianceType = SignalTieVarianceType = {}));
		var SignalTiePolarity;
		(function (SignalTiePolarity) {
		    SignalTiePolarity["PositiveOnSignal"] = "positive_on_signal";
		    SignalTiePolarity["PositiveOnTarget"] = "positive_on_target";
		})(SignalTiePolarity || (exports.SignalTiePolarity = SignalTiePolarity = {}));
		exports.flatSignalTieComponentValueAbsoluteSchema = zod_1.z.object({
		    type: zod_1.z.literal('absolute'),
		    value: zod_1.z.number(),
		    variance: zod_1.z.number().nullable(),
		    varianceType: zod_1.z.nativeEnum(SignalTieVarianceType).nullable(),
		});
		exports.flatSignalTieComponentValueRangeSchema = zod_1.z.object({
		    type: zod_1.z.literal('range'),
		    min: zod_1.z.number().nullable(),
		    max: zod_1.z.number().nullable(),
		});
		exports.flatSignalTieComponentValueSchema = zod_1.z.discriminatedUnion('type', [
		    exports.flatSignalTieComponentValueAbsoluteSchema,
		    exports.flatSignalTieComponentValueRangeSchema,
		]);
		exports.flatSignalTieSchema = zod_1.z.object({
		    toSignalId: zod_1.z.string(),
		    connectionType: zod_1.z.nativeEnum(SignalTieConnectionType),
		    componentType: zod_1.z.nativeEnum(SignalTieComponentType).nullable(),
		    componentValue: exports.flatSignalTieComponentValueSchema.nullable(),
		    polarity: zod_1.z.nativeEnum(SignalTiePolarity).optional(),
		});
		exports.MODEL_TO_TIE_COMPONENT_TYPE = {
		    'capacitor.nonpolarized': SignalTieComponentType.Capacitor,
		    'capacitor.polarized': SignalTieComponentType.CapacitorPolarized,
		    'inductor.standard': SignalTieComponentType.Inductor,
		    'resistor.standard': SignalTieComponentType.Resistor,
		};
		exports.TIE_COMPONENT_TYPE_TO_MODEL = {
		    [SignalTieComponentType.Capacitor]: 'capacitor.nonpolarized',
		    [SignalTieComponentType.CapacitorPolarized]: 'capacitor.polarized',
		    [SignalTieComponentType.Inductor]: 'inductor.standard',
		    [SignalTieComponentType.Resistor]: 'resistor.standard',
		};
		function isPolarizedTieComponentType(componentType) {
		    return componentType === SignalTieComponentType.CapacitorPolarized;
		}
		function reverseSignalTiePolarity(polarity) {
		    return polarity === SignalTiePolarity.PositiveOnSignal ?
		        SignalTiePolarity.PositiveOnTarget
		        : SignalTiePolarity.PositiveOnSignal;
		}
		exports.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC = {
		    [SignalTieComponentType.Capacitor]: sigma_engine_shared_1.BoundaryCharacteristic.Capacitance,
		    [SignalTieComponentType.CapacitorPolarized]: sigma_engine_shared_1.BoundaryCharacteristic.Capacitance,
		    [SignalTieComponentType.Inductor]: sigma_engine_shared_1.BoundaryCharacteristic.Inductance,
		    [SignalTieComponentType.Resistor]: sigma_engine_shared_1.BoundaryCharacteristic.Resistance,
		};
		exports.TIE_COMPONENT_TYPE_TO_UNITS = {
		    [SignalTieComponentType.Capacitor]: sigma_engine_shared_1.CommonParameterUnits.capacitance,
		    [SignalTieComponentType.CapacitorPolarized]: sigma_engine_shared_1.CommonParameterUnits.capacitance,
		    [SignalTieComponentType.Inductor]: sigma_engine_shared_1.CommonParameterUnits.inductance,
		    [SignalTieComponentType.Resistor]: sigma_engine_shared_1.CommonParameterUnits.resistance,
		};
		
	} (signalTieTypes));
	return signalTieTypes;
}

var signalTieTransforms = {};

var tieComponentValue = {};

var hasRequiredTieComponentValue;

function requireTieComponentValue () {
	if (hasRequiredTieComponentValue) return tieComponentValue;
	hasRequiredTieComponentValue = 1;
	Object.defineProperty(tieComponentValue, "__esModule", { value: true });
	tieComponentValue.TieComponentValue = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const signal_tie_types_1 = requireSignalTieTypes();
	class TieComponentValue {
	    static { this.JOINED_BY_HYPHEN = /^\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*[-\u2010\u2011\u2012\u2013]\s*([A-Za-z\u00B5\u03BC\u03A9\u2126]+)\s*$/u; }
	    static parse(text, componentType) {
	        if (text.trim() === '') {
	            return undefined;
	        }
	        const asWritten = TieComponentValue.tryParse(text, componentType);
	        if (asWritten !== undefined) {
	            return asWritten;
	        }
	        const unjoined = TieComponentValue.withoutJoiningHyphen(text);
	        return unjoined === undefined ? undefined : TieComponentValue.tryParse(unjoined, componentType);
	    }
	    static format(text, componentType) {
	        const value = TieComponentValue.parse(text, componentType);
	        if (value === undefined) {
	            return undefined;
	        }
	        return `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value)}${signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[componentType].short}`;
	    }
	    static parseVariance(text, componentType) {
	        const trimmed = text.trim();
	        if (trimmed === '') {
	            return undefined;
	        }
	        const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[signal_tie_types_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[componentType]];
	        try {
	            const properties = sigma_engine_shared_1.VarianceChecker.valueVarianceToProperties({ variance: trimmed }, siDefinition);
	            if (properties === undefined || !Number.isFinite(properties.value)) {
	                return undefined;
	            }
	            const isRelative = properties.units.short === sigma_engine_shared_1.CommonParameterUnits.percentage.short;
	            return {
	                variance: properties.value,
	                varianceType: isRelative ? signal_tie_types_1.SignalTieVarianceType.Relative : signal_tie_types_1.SignalTieVarianceType.Absolute,
	            };
	        }
	        catch {
	            return undefined;
	        }
	    }
	    static withoutJoiningHyphen(text) {
	        const match = TieComponentValue.JOINED_BY_HYPHEN.exec(text);
	        if (match === null) {
	            return undefined;
	        }
	        const [, number, unit] = match;
	        return `${number}${unit}`;
	    }
	    static tryParse(text, componentType) {
	        const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[signal_tie_types_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[componentType]];
	        try {
	            const value = sigma_engine_shared_1.Utils.parseNumericalSIValue(text, siDefinition);
	            return Number.isFinite(value) ? value : undefined;
	        }
	        catch {
	            return undefined;
	        }
	    }
	}
	tieComponentValue.TieComponentValue = TieComponentValue;
	
	return tieComponentValue;
}

var hasRequiredSignalTieTransforms;

function requireSignalTieTransforms () {
	if (hasRequiredSignalTieTransforms) return signalTieTransforms;
	hasRequiredSignalTieTransforms = 1;
	Object.defineProperty(signalTieTransforms, "__esModule", { value: true });
	signalTieTransforms.convertExternalComponentToFlatSignalTie = convertExternalComponentToFlatSignalTie;
	signalTieTransforms.convertFlatSignalTieToExternalComponent = convertFlatSignalTieToExternalComponent;
	signalTieTransforms.flatTieToRowValues = flatTieToRowValues;
	signalTieTransforms.signalTieRowValuesToFlatTie = signalTieRowValuesToFlatTie;
	signalTieTransforms.applySignalTieRowsToSignals = applySignalTieRowsToSignals;
	signalTieTransforms.splitExternalComponentByConnection = splitExternalComponentByConnection;
	signalTieTransforms.normalizeSignalTies = normalizeSignalTies;
	const sigma_engine_shared_1 = requireDist$1();
	const signal_tie_types_1 = requireSignalTieTypes();
	const tie_component_value_1 = requireTieComponentValue();
	const POLARITY_TO_TARGET_MODEL_SIGNAL_ID = {
	    [signal_tie_types_1.SignalTiePolarity.PositiveOnSignal]: '-',
	    [signal_tie_types_1.SignalTiePolarity.PositiveOnTarget]: '+',
	};
	const TARGET_MODEL_SIGNAL_ID_TO_POLARITY = {
	    '-': signal_tie_types_1.SignalTiePolarity.PositiveOnSignal,
	    '+': signal_tie_types_1.SignalTiePolarity.PositiveOnTarget,
	};
	function convertRangeBoundaryToFlatSignalTie(boundary, componentType, toSignalId) {
	    return {
	        toSignalId,
	        connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	        componentType,
	        componentValue: {
	            type: 'range',
	            min: boundary.minVal,
	            max: boundary.maxVal,
	        },
	    };
	}
	function convertExternalComponentToFlatSignalTie(component, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    if (component.rootComponentSignalId && !component.model) {
	        const matchingSignal = signals?.find((s) => String(s.id) === component.rootComponentSignalId);
	        if (signals && !matchingSignal) {
	            logger.warn('ConvertExternalComponentToFlatSignalTie_DirectTieSignalNotFound', {
	                rootComponentSignalId: component.rootComponentSignalId,
	            });
	            return null;
	        }
	        return {
	            toSignalId: component.rootComponentSignalId,
	            connectionType: signal_tie_types_1.SignalTieConnectionType.Direct,
	            componentType: null,
	            componentValue: null,
	        };
	    }
	    if (component.model && component.withConnections) {
	        const targetConnection = component.withConnections[0];
	        const targetSignalRef = targetConnection?.components?.[0]?.rootComponentSignalId;
	        if (!targetSignalRef) {
	            logger.warn('ConvertExternalComponentToFlatSignalTie_MissingTargetSignal', { component });
	            return null;
	        }
	        const matchingSignal = signals?.find((s) => String(s.id) === targetSignalRef);
	        if (signals && !matchingSignal) {
	            logger.warn('ConvertExternalComponentToFlatSignalTie_IndirectTieSignalNotFound', {
	                targetSignalRef,
	            });
	            return null;
	        }
	        const componentType = signal_tie_types_1.MODEL_TO_TIE_COMPONENT_TYPE[component.model];
	        if (!componentType) {
	            logger.warn('ConvertExternalComponentToFlatSignalTie_UnsupportedModel', {
	                model: component.model,
	            });
	            return null;
	        }
	        const polarity = (0, signal_tie_types_1.isPolarizedTieComponentType)(componentType) && targetConnection?.modelSignalId !== undefined ?
	            TARGET_MODEL_SIGNAL_ID_TO_POLARITY[targetConnection.modelSignalId]
	            : undefined;
	        const tie = convertComponentValueToFlatSignalTie(component, componentType, targetSignalRef, logger);
	        return polarity === undefined ? tie : { ...tie, polarity };
	    }
	    logger.warn('ConvertExternalComponentToFlatSignalTie_UnsupportedFormat', { component });
	    return null;
	}
	function convertComponentValueToFlatSignalTie(component, componentType, targetSignalRef, logger) {
	    const boundary = component.value;
	    if (boundary === undefined) {
	        return {
	            toSignalId: targetSignalRef,
	            connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	            componentType,
	            componentValue: null,
	        };
	    }
	    switch (boundary.type) {
	        case 'absolute': {
	            if (boundary.variance) {
	                const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[componentType];
	                const varianceProps = sigma_engine_shared_1.VarianceChecker.valueVarianceToProperties({ variance: boundary.variance }, units);
	                if (!varianceProps) {
	                    logger.warn('ConvertExternalComponentToFlatSignalTie_AbsoluteBoundaryVarianceParseFailure', {
	                        boundary,
	                        componentName: component.name,
	                    });
	                }
	                else {
	                    const isRelative = varianceProps.units.short === sigma_engine_shared_1.CommonParameterUnits.percentage.short;
	                    return {
	                        toSignalId: targetSignalRef,
	                        connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	                        componentType,
	                        componentValue: {
	                            type: 'absolute',
	                            value: boundary.value,
	                            variance: varianceProps.value,
	                            varianceType: isRelative ? signal_tie_types_1.SignalTieVarianceType.Relative : signal_tie_types_1.SignalTieVarianceType.Absolute,
	                        },
	                    };
	                }
	            }
	            return {
	                toSignalId: targetSignalRef,
	                connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	                componentType,
	                componentValue: {
	                    type: 'absolute',
	                    value: boundary.value,
	                    variance: null,
	                    varianceType: null,
	                },
	            };
	        }
	        case 'range':
	            return convertRangeBoundaryToFlatSignalTie(boundary, componentType, targetSignalRef);
	        case 'minimum':
	            return {
	                toSignalId: targetSignalRef,
	                connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	                componentType,
	                componentValue: {
	                    type: 'range',
	                    min: boundary.value,
	                    max: null,
	                },
	            };
	        case 'maximum':
	            return {
	                toSignalId: targetSignalRef,
	                connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	                componentType,
	                componentValue: {
	                    type: 'range',
	                    min: null,
	                    max: boundary.value,
	                },
	            };
	    }
	}
	function convertFlatSignalTieToExternalComponent(flatTie, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    const getSignalDisplayName = (signalId) => {
	        if (signals) {
	            const signal = signals.find((s) => s.id === signalId);
	            if (signal) {
	                return signal.name;
	            }
	        }
	        return String(signalId);
	    };
	    if (flatTie.connectionType === signal_tie_types_1.SignalTieConnectionType.Direct) {
	        const signalName = getSignalDisplayName(flatTie.toSignalId);
	        return {
	            name: `Direct connection to signal ${signalName}`,
	            rootComponentSignalId: flatTie.toSignalId,
	        };
	    }
	    if (flatTie.connectionType === signal_tie_types_1.SignalTieConnectionType.ThroughComponent) {
	        if (!flatTie.componentType) {
	            logger.warn('ConvertFlatSignalTieToExternalComponent_MissingComponentData', { flatTie });
	            throw new Error('Component type is required for ThroughComponent ties');
	        }
	        const model = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_MODEL[flatTie.componentType];
	        const characteristic = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[flatTie.componentType];
	        const signalName = getSignalDisplayName(flatTie.toSignalId);
	        const targetModelSignalId = flatTie.polarity === undefined ? undefined : POLARITY_TO_TARGET_MODEL_SIGNAL_ID[flatTie.polarity];
	        const targetConnection = {
	            name: `Tie to ${signalName}`,
	            ...(targetModelSignalId !== undefined && { modelSignalId: targetModelSignalId }),
	            components: [
	                {
	                    name: `Tie to ${signalName}`,
	                    rootComponentSignalId: flatTie.toSignalId,
	                },
	            ],
	        };
	        const componentValue = flatTie.componentValue;
	        if (componentValue === null) {
	            return {
	                name: `Connection through ${model}`,
	                model,
	                withConnections: [targetConnection],
	            };
	        }
	        else if (componentValue.type === 'absolute') {
	            let varianceString;
	            if (componentValue.variance !== null && componentValue.varianceType !== null) {
	                const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[flatTie.componentType];
	                const varianceUnits = componentValue.varianceType === signal_tie_types_1.SignalTieVarianceType.Relative ? sigma_engine_shared_1.CommonParameterUnits.percentage : units;
	                varianceString = sigma_engine_shared_1.VarianceChecker.buildValueVariance(componentValue.variance, varianceUnits, false).variance;
	            }
	            return {
	                name: `Connection through ${model}`,
	                model,
	                value: {
	                    type: 'absolute',
	                    characteristic,
	                    value: componentValue.value,
	                    ...(varianceString && { variance: varianceString }),
	                },
	                withConnections: [targetConnection],
	            };
	        }
	        if (componentValue.type === 'range') {
	            if (componentValue.min === null && componentValue.max === null) {
	                logger.warn('ConvertFlatSignalTieToExternalComponent_RangeWithNoBounds', { flatTie });
	                throw new Error('Range component value must have at least one bound (min or max)');
	            }
	            let value;
	            if (componentValue.min !== null && componentValue.max !== null) {
	                value = {
	                    type: 'range',
	                    characteristic,
	                    minVal: componentValue.min,
	                    maxVal: componentValue.max,
	                };
	            }
	            else if (componentValue.min !== null) {
	                value = {
	                    type: 'minimum',
	                    characteristic,
	                    value: componentValue.min,
	                };
	            }
	            else {
	                value = {
	                    type: 'maximum',
	                    characteristic,
	                    value: componentValue.max,
	                };
	            }
	            return {
	                name: `Connection through ${model}`,
	                model,
	                value,
	                withConnections: [targetConnection],
	            };
	        }
	    }
	    logger.warn('ConvertFlatSignalTieToExternalComponent_UnsupportedConnectionType', {
	        connectionType: flatTie.connectionType,
	    });
	    throw new Error(`Unsupported connection type: ${flatTie.connectionType}`);
	}
	function formatTieVarianceString(value, unitShort) {
	    if (value.variance === null || value.varianceType === null) {
	        return '';
	    }
	    if (value.varianceType === signal_tie_types_1.SignalTieVarianceType.Relative) {
	        return `${value.variance}%`;
	    }
	    return `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.variance)}${unitShort}`;
	}
	function flatTieToRowValues(flatTie) {
	    const empty = {
	        componentType: '',
	        componentValueAbs: '',
	        componentValueMin: '',
	        componentValueMax: '',
	        componentValueVariance: '',
	        polarity: '',
	    };
	    if (flatTie.connectionType === signal_tie_types_1.SignalTieConnectionType.Direct || flatTie.componentType === null) {
	        return empty;
	    }
	    const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[flatTie.componentType];
	    const withType = {
	        ...empty,
	        componentType: flatTie.componentType,
	        polarity: (0, signal_tie_types_1.isPolarizedTieComponentType)(flatTie.componentType) ? (flatTie.polarity ?? '') : '',
	    };
	    const value = flatTie.componentValue;
	    if (value === null) {
	        return withType;
	    }
	    if (value.type === 'absolute') {
	        return {
	            ...withType,
	            componentValueAbs: `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.value)}${units.short}`,
	            componentValueVariance: formatTieVarianceString(value, units.short),
	        };
	    }
	    return {
	        ...withType,
	        componentValueMin: value.min === null ? '' : `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.min)}${units.short}`,
	        componentValueMax: value.max === null ? '' : `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.max)}${units.short}`,
	    };
	}
	function signalTieRowValuesToFlatTie(args, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    if (args.connectionType === signal_tie_types_1.SignalTieConnectionType.Direct) {
	        return {
	            toSignalId: args.toSignalId,
	            connectionType: signal_tie_types_1.SignalTieConnectionType.Direct,
	            componentType: null,
	            componentValue: null,
	        };
	    }
	    const componentType = args.componentType === '' ? null : args.componentType;
	    if (componentType === null) {
	        logger.warn('SignalTieRowValuesToFlatTie_MissingComponentType', { args });
	        return null;
	    }
	    const abs = args.componentValueAbs.trim();
	    const min = args.componentValueMin.trim();
	    const max = args.componentValueMax.trim();
	    const withoutValue = {
	        toSignalId: args.toSignalId,
	        connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
	        componentType,
	        componentValue: null,
	        ...((0, signal_tie_types_1.isPolarizedTieComponentType)(componentType) && args.polarity !== '' && { polarity: args.polarity }),
	    };
	    if (abs !== '') {
	        const value = tie_component_value_1.TieComponentValue.parse(abs, componentType);
	        if (value === undefined) {
	            logger.warn('SignalTieRowValuesToFlatTie_UnreadableValueCleared', { componentType, componentValueAbs: abs });
	            return withoutValue;
	        }
	        const variance = tie_component_value_1.TieComponentValue.parseVariance(args.componentValueVariance, componentType);
	        if (variance === undefined && args.componentValueVariance.trim() !== '') {
	            logger.warn('SignalTieRowValuesToFlatTie_UnreadableVarianceCleared', {
	                componentType,
	                componentValueVariance: args.componentValueVariance,
	            });
	        }
	        return {
	            ...withoutValue,
	            componentValue: {
	                type: 'absolute',
	                value,
	                variance: variance?.variance ?? null,
	                varianceType: variance?.varianceType ?? null,
	            },
	        };
	    }
	    if (min !== '' || max !== '') {
	        const parsedMin = min === '' ? null : tie_component_value_1.TieComponentValue.parse(min, componentType);
	        const parsedMax = max === '' ? null : tie_component_value_1.TieComponentValue.parse(max, componentType);
	        if (parsedMin === undefined || parsedMax === undefined) {
	            logger.warn('SignalTieRowValuesToFlatTie_UnreadableValueCleared', {
	                componentType,
	                componentValueMin: min,
	                componentValueMax: max,
	            });
	            return withoutValue;
	        }
	        return { ...withoutValue, componentValue: { type: 'range', min: parsedMin, max: parsedMax } };
	    }
	    return withoutValue;
	}
	function applySignalTieRowsToSignals(signals, tieRows, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    const rowsByName = new Map();
	    const availableSignalNames = signals.map((signal) => signal.name);
	    for (const entry of tieRows) {
	        if (!signals.some((signal) => signal.name === entry.signalName)) {
	            logger.warn('ApplySignalTieRowsToSignals_SignalNotFound', {
	                signalName: entry.signalName,
	                availableSignalNames,
	            });
	            continue;
	        }
	        rowsByName.set(entry.signalName, entry.ties);
	    }
	    return signals.map((signal) => {
	        const rows = rowsByName.get(signal.name);
	        if (rows === undefined) {
	            return signal;
	        }
	        const externalComponents = [];
	        for (const row of rows) {
	            const toSignalId = sigma_engine_shared_1.DesignModels.findSignalIdBySignalName(row.toSignalName, signals, logger);
	            if (!toSignalId) {
	                logger.warn('ApplySignalTieRowsToSignals_ToSignalNotFound', {
	                    toSignalName: row.toSignalName,
	                    availableSignalNames,
	                });
	                continue;
	            }
	            const flatTie = signalTieRowValuesToFlatTie({
	                toSignalId,
	                connectionType: row.connectionType,
	                componentType: row.componentType,
	                componentValueAbs: row.componentValueAbs,
	                componentValueMin: row.componentValueMin,
	                componentValueMax: row.componentValueMax,
	                componentValueVariance: row.componentValueVariance,
	                polarity: row.polarity,
	            }, logger);
	            if (flatTie === null) {
	                continue;
	            }
	            try {
	                externalComponents.push(convertFlatSignalTieToExternalComponent(flatTie, signals, logger));
	            }
	            catch (error) {
	                logger.warn('ApplySignalTieRowsToSignals_ConversionFailed', {
	                    signalName: signal.name,
	                    error: error instanceof Error ? error.message : String(error),
	                });
	            }
	        }
	        return {
	            ...signal,
	            ties: externalComponents.length > 0 ? externalComponents : undefined,
	        };
	    });
	}
	function splitExternalComponentByConnection(component) {
	    const connections = component.withConnections;
	    if (component.model === undefined || connections === undefined || connections.length <= 1) {
	        return [component];
	    }
	    return connections.map((connection) => ({ ...component, withConnections: [connection] }));
	}
	function normalizeSignalTies(signals) {
	    return signals.map((signal) => signal.ties && signal.ties.length > 0 ?
	        { ...signal, ties: signal.ties.flatMap(splitExternalComponentByConnection) }
	        : signal);
	}
	
	return signalTieTransforms;
}

var signalTieDirectionRule = {};

var hasRequiredSignalTieDirectionRule;

function requireSignalTieDirectionRule () {
	if (hasRequiredSignalTieDirectionRule) return signalTieDirectionRule;
	hasRequiredSignalTieDirectionRule = 1;
	Object.defineProperty(signalTieDirectionRule, "__esModule", { value: true });
	signalTieDirectionRule.SignalTieDirectionRule = signalTieDirectionRule.TieEndpointRank = void 0;
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const signal_tie_types_1 = requireSignalTieTypes();
	var TieEndpointRank;
	(function (TieEndpointRank) {
	    TieEndpointRank["MainGround"] = "main_ground";
	    TieEndpointRank["Ground"] = "ground";
	    TieEndpointRank["Supply"] = "supply";
	    TieEndpointRank["Other"] = "other";
	})(TieEndpointRank || (signalTieDirectionRule.TieEndpointRank = TieEndpointRank = {}));
	const RANK_ORDER = [
	    TieEndpointRank.MainGround,
	    TieEndpointRank.Ground,
	    TieEndpointRank.Supply,
	    TieEndpointRank.Other,
	];
	const MAIN_GROUND_NAMES = ['gnd', 'vss'];
	const SYMBOL_ORDER = ['+', '-'];
	class SignalTieDirectionRule {
	    constructor(roster) {
	        this.rankByName = new Map();
	        const mainGroundName = SignalTieDirectionRule.mainGroundNameOf(roster.filter((signal) => signal.ground));
	        for (const signal of roster) {
	            const name = (0, signal_name_normalization_1.normalizeSignalNameForComparison)(signal.signalName);
	            this.rankByName.set(name, name === mainGroundName ? TieEndpointRank.MainGround
	                : signal.ground ? TieEndpointRank.Ground
	                    : signal.power ? TieEndpointRank.Supply
	                        : TieEndpointRank.Other);
	        }
	    }
	    rankOf(signalName) {
	        return this.rankByName.get((0, signal_name_normalization_1.normalizeSignalNameForComparison)(signalName)) ?? TieEndpointRank.Other;
	    }
	    orient(first, second) {
	        const firstRank = RANK_ORDER.indexOf(this.rankOf(first));
	        const secondRank = RANK_ORDER.indexOf(this.rankOf(second));
	        const firstIsTarget = firstRank === secondRank ? SignalTieDirectionRule.compareNames(first, second) > 0 : firstRank < secondRank;
	        return firstIsTarget ? { signalName: second, toSignalName: first } : { signalName: first, toSignalName: second };
	    }
	    orientEnds(record) {
	        const ends = this.orient(record.signalName, record.toSignalName);
	        return {
	            ...record,
	            ...ends,
	            ...SignalTieDirectionRule.polarityAfterOrienting(record, ends.signalName !== record.signalName),
	        };
	    }
	    canonicalize(signalTies) {
	        const tiesByCarrier = new Map();
	        for (const signal of signalTies) {
	            for (const tie of signal.ties) {
	                const ends = this.orient(signal.signalName, tie.toSignalName);
	                const ties = tiesByCarrier.get(ends.signalName) ?? [];
	                ties.push({
	                    ...tie,
	                    toSignalName: ends.toSignalName,
	                    ...SignalTieDirectionRule.polarityAfterOrienting(tie, ends.signalName !== signal.signalName),
	                });
	                tiesByCarrier.set(ends.signalName, ties);
	            }
	        }
	        return [...tiesByCarrier.entries()].map(([signalName, ties]) => ({ signalName, ties }));
	    }
	    static polarityAfterOrienting(tie, swapped) {
	        return swapped && tie.polarity !== undefined && tie.polarity !== '' ?
	            { polarity: (0, signal_tie_types_1.reverseSignalTiePolarity)(tie.polarity) }
	            : {};
	    }
	    static compareNames(a, b) {
	        const left = (0, signal_name_normalization_1.normalizeSignalNameForComparison)(a);
	        const right = (0, signal_name_normalization_1.normalizeSignalNameForComparison)(b);
	        const shared = Math.min(left.length, right.length);
	        for (let index = 0; index < shared; index++) {
	            const order = SignalTieDirectionRule.compareCharacters(left[index], right[index]);
	            if (order !== 0) {
	                return order;
	            }
	        }
	        return left.length - right.length;
	    }
	    static compareCharacters(a, b) {
	        const byClass = SignalTieDirectionRule.characterClass(a) - SignalTieDirectionRule.characterClass(b);
	        if (byClass !== 0) {
	            return byClass;
	        }
	        const bySymbol = SignalTieDirectionRule.symbolOrder(a) - SignalTieDirectionRule.symbolOrder(b);
	        return bySymbol !== 0 ? bySymbol : (a.codePointAt(0) ?? 0) - (b.codePointAt(0) ?? 0);
	    }
	    static characterClass(character) {
	        if (/\p{Nd}/u.test(character)) {
	            return 1;
	        }
	        if (/\p{L}/u.test(character)) {
	            return 2;
	        }
	        return 0;
	    }
	    static symbolOrder(character) {
	        const index = SYMBOL_ORDER.indexOf(character);
	        return index === -1 ? SYMBOL_ORDER.length : index;
	    }
	    static mainGroundNameOf(grounds) {
	        const names = new Set(grounds.map((ground) => (0, signal_name_normalization_1.normalizeSignalNameForComparison)(ground.signalName)));
	        if (names.size === 1) {
	            return [...names][0];
	        }
	        return MAIN_GROUND_NAMES.find((name) => names.has(name));
	    }
	}
	signalTieDirectionRule.SignalTieDirectionRule = SignalTieDirectionRule;
	
	return signalTieDirectionRule;
}

var signalTieValidation = {};

var hasRequiredSignalTieValidation;

function requireSignalTieValidation () {
	if (hasRequiredSignalTieValidation) return signalTieValidation;
	hasRequiredSignalTieValidation = 1;
	Object.defineProperty(signalTieValidation, "__esModule", { value: true });
	signalTieValidation.SignalTieValidator = signalTieValidation.SignalTieIssueSeverity = signalTieValidation.SignalTieIssueKind = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const signal_tie_types_1 = requireSignalTieTypes();
	var SignalTieIssueKind;
	(function (SignalTieIssueKind) {
	    SignalTieIssueKind["DanglingTarget"] = "dangling_target";
	    SignalTieIssueKind["SelfTarget"] = "self_target";
	    SignalTieIssueKind["MissingPolarity"] = "missing_polarity";
	    SignalTieIssueKind["ImplausibleValue"] = "implausible_value";
	})(SignalTieIssueKind || (signalTieValidation.SignalTieIssueKind = SignalTieIssueKind = {}));
	var SignalTieIssueSeverity;
	(function (SignalTieIssueSeverity) {
	    SignalTieIssueSeverity["Error"] = "error";
	    SignalTieIssueSeverity["Warning"] = "warning";
	})(SignalTieIssueSeverity || (signalTieValidation.SignalTieIssueSeverity = SignalTieIssueSeverity = {}));
	class SignalTieValidator {
	    static { this.PLAUSIBLE_VALUE_RANGES = {
	        [signal_tie_types_1.SignalTieComponentType.Capacitor]: { min: 0.1e-12, max: 0.1 },
	        [signal_tie_types_1.SignalTieComponentType.CapacitorPolarized]: { min: 1e-9, max: 100 },
	        [signal_tie_types_1.SignalTieComponentType.Inductor]: { min: 1e-9, max: 10 },
	        [signal_tie_types_1.SignalTieComponentType.Resistor]: { min: 0, max: 1e9 },
	    }; }
	    static validateTie(tie, carrierSignalId, signalIds) {
	        const issues = [];
	        if (tie.toSignalId === carrierSignalId) {
	            issues.push({
	                kind: SignalTieIssueKind.SelfTarget,
	                severity: SignalTieIssueSeverity.Error,
	                message: 'Tied to its own signal.',
	            });
	        }
	        if (tie.toSignalId !== '' && !signalIds.has(tie.toSignalId)) {
	            issues.push({
	                kind: SignalTieIssueKind.DanglingTarget,
	                severity: SignalTieIssueSeverity.Error,
	                message: 'Tied to a signal that is no longer on the package.',
	            });
	        }
	        if (tie.connectionType !== signal_tie_types_1.SignalTieConnectionType.ThroughComponent || tie.componentType === null) {
	            return issues;
	        }
	        if ((0, signal_tie_types_1.isPolarizedTieComponentType)(tie.componentType) && tie.polarity === undefined) {
	            issues.push({
	                kind: SignalTieIssueKind.MissingPolarity,
	                severity: SignalTieIssueSeverity.Error,
	                message: 'Polarity not set: say which signal the positive pin faces.',
	            });
	        }
	        const implausible = SignalTieValidator.findImplausibleValue(tie.componentType, tie);
	        if (implausible !== undefined) {
	            const range = SignalTieValidator.PLAUSIBLE_VALUE_RANGES[tie.componentType];
	            const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[tie.componentType].short;
	            const format = (value) => `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value)}${units}`;
	            issues.push({
	                kind: SignalTieIssueKind.ImplausibleValue,
	                severity: SignalTieIssueSeverity.Warning,
	                message: `${format(implausible)} is outside the usual ${format(range.min)} to ${format(range.max)}; check the units.`,
	            });
	        }
	        return issues;
	    }
	    static countIssues(tiesBySignalId, signalIds) {
	        let errorCount = 0;
	        let warningCount = 0;
	        for (const [carrierSignalId, ties] of tiesBySignalId) {
	            for (const tie of ties) {
	                const issues = SignalTieValidator.validateTie(tie, carrierSignalId, signalIds);
	                if (issues.some((issue) => issue.severity === SignalTieIssueSeverity.Error)) {
	                    errorCount++;
	                }
	                else if (issues.length > 0) {
	                    warningCount++;
	                }
	            }
	        }
	        return { errorCount, warningCount };
	    }
	    static findImplausibleValue(componentType, tie) {
	        const value = tie.componentValue;
	        if (value === null) {
	            return undefined;
	        }
	        const entered = value.type === 'absolute' ? [value.value] : [value.min, value.max];
	        const range = SignalTieValidator.PLAUSIBLE_VALUE_RANGES[componentType];
	        return entered.find((v) => v !== null && (!Number.isFinite(v) || v < range.min || v > range.max));
	    }
	}
	signalTieValidation.SignalTieValidator = SignalTieValidator;
	
	return signalTieValidation;
}

var hasRequiredSignalTies;

function requireSignalTies () {
	if (hasRequiredSignalTies) return SignalTies;
	hasRequiredSignalTies = 1;
	(function (exports) {
		var __createBinding = (SignalTies && SignalTies.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (SignalTies && SignalTies.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireSignalTieTypes(), exports);
		__exportStar(requireSignalTieTransforms(), exports);
		__exportStar(requireSignalTieDirectionRule(), exports);
		__exportStar(requireTieComponentValue(), exports);
		__exportStar(requireSignalTieValidation(), exports);
		
	} (SignalTies));
	return SignalTies;
}

var hasRequiredSignalTies_comparison;

function requireSignalTies_comparison () {
	if (hasRequiredSignalTies_comparison) return signalTies_comparison;
	hasRequiredSignalTies_comparison = 1;
	Object.defineProperty(signalTies_comparison, "__esModule", { value: true });
	signalTies_comparison.SignalTiesComparison = signalTies_comparison.SignalTieRowSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const si_comparison_1 = requireSiComparison();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const SignalTies_1 = requireSignalTies();
	const comparison_spec_1 = requireComparisonSpec();
	signalTies_comparison.SignalTieRowSchema = z.object({
	    signalName: z.string(),
	    toSignalName: z.string(),
	    connectionType: z.string(),
	    componentType: z.string(),
	    componentValueAbs: z.string(),
	    componentValueMin: z.string(),
	    componentValueMax: z.string(),
	    componentValueVariance: z.string(),
	    polarity: z.union([z.nativeEnum(SignalTies_1.SignalTiePolarity), z.literal('')]).default(''),
	});
	class SignalTieIdentityAlignment extends comparison_spec_1.SetAlignment {
	    identityOf(row, normalize, columns) {
	        const universe = columns ?? Object.keys(row);
	        return super.identityOf(row, normalize, universe.filter((column) => column !== 'polarity' || normalize(column, row[column], row) !== ''));
	    }
	}
	class SignalTiesComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new SignalTieIdentityAlignment();
	        this.membershipScopeColumns = ['signalName', 'toSignalName'];
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return this.alignment.identityOf(row, (column, value, rowContext) => this.normalize(column, value, rowContext), Object.keys(row).sort());
	    }
	    normalize(column, value, row) {
	        if ((column === 'signalName' || column === 'toSignalName') && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        const characteristic = SignalTies_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[row?.componentType];
	        if (typeof value === 'string' && value !== '' && characteristic !== undefined) {
	            if (column === 'componentValueAbs' || column === 'componentValueMin' || column === 'componentValueMax') {
	                return (0, si_comparison_1.formatSIOrSymbolicValue)(value, characteristic);
	            }
	            if (column === 'componentValueVariance') {
	                return SignalTiesComparison.formatVariance(value, characteristic) ?? super.normalize(column, value, row);
	            }
	        }
	        return super.normalize(column, value, row);
	    }
	    static formatVariance(variance, characteristic) {
	        let properties;
	        try {
	            properties = sigma_engine_shared_1.VarianceChecker.valueVarianceToProperties({ variance }, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic]);
	        }
	        catch {
	            return undefined;
	        }
	        if (properties === undefined) {
	            return undefined;
	        }
	        const sign = properties.singleSided && properties.value > 0 ? '+' : '';
	        return properties.units.short === sigma_engine_shared_1.CommonParameterUnits.percentage.short ?
	            `${sign}${properties.value}%`
	            : `${sign}${sigma_engine_shared_1.Utils.formatNumericalSIValue(properties.value)}${properties.units.short}`;
	    }
	}
	signalTies_comparison.SignalTiesComparison = SignalTiesComparison;
	
	return signalTies_comparison;
}

var resources_comparison = {};

var hasRequiredResources_comparison;

function requireResources_comparison () {
	if (hasRequiredResources_comparison) return resources_comparison;
	hasRequiredResources_comparison = 1;
	Object.defineProperty(resources_comparison, "__esModule", { value: true });
	resources_comparison.ResourcesComparison = resources_comparison.ResourceRowSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const value_comparison_1 = requireValueComparison();
	const comparison_spec_1 = requireComparisonSpec();
	resources_comparison.ResourceRowSchema = z.object({
	    name: z.string(),
	    mustBeConsumed: z.boolean(),
	    conditions: z.string(),
	    sourceConditions: z.unknown().optional(),
	});
	class ResourcesComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.CellAlignment(['name'], ['mustBeConsumed', 'conditions']);
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return (0, value_comparison_1.compositeKey)(row, this.alignment.keyColumns.map(String), (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	}
	resources_comparison.ResourcesComparison = ResourcesComparison;
	
	return resources_comparison;
}

var functions_comparison = {};

var componentFunctionDenormalized = {};

var signalIdRemapInternal = {};

var hasRequiredSignalIdRemapInternal;

function requireSignalIdRemapInternal () {
	if (hasRequiredSignalIdRemapInternal) return signalIdRemapInternal;
	hasRequiredSignalIdRemapInternal = 1;
	Object.defineProperty(signalIdRemapInternal, "__esModule", { value: true });
	signalIdRemapInternal.remapRelativeSignal = remapRelativeSignal;
	signalIdRemapInternal.remapBoundaryTarget = remapBoundaryTarget;
	signalIdRemapInternal.remapBoundaryDefinition = remapBoundaryDefinition;
	function lookupOrOrphan(id, map, fieldPath, opts) {
	    const replacement = map.get(id);
	    if (replacement === undefined) {
	        opts?.onOrphan?.(id, fieldPath);
	        return id;
	    }
	    return replacement;
	}
	function remapRelativeSignal(rs, map, fieldPath, opts) {
	    const sig = rs.signal;
	    if (sig === undefined || sig === null) {
	        return;
	    }
	    if (Array.isArray(sig)) {
	        rs.signal = sig.map((id, i) => lookupOrOrphan(String(id), map, `${fieldPath}[${i}]`, opts));
	        return;
	    }
	    rs.signal = lookupOrOrphan(String(sig), map, fieldPath, opts);
	}
	function remapBoundaryTarget(target, map, fieldPath, opts) {
	    if (!target) {
	        return;
	    }
	    if (!('signal' in target)) {
	        return;
	    }
	    remapRelativeSignal(target, map, `${fieldPath}.signal`, opts);
	}
	function remapBoundaryDefinition(def, map, fieldPath, opts) {
	    if (!def) {
	        return;
	    }
	    remapBoundaryTarget(def.target, map, `${fieldPath}.target`, opts);
	    remapBoundaryTarget(def.relative_to, map, `${fieldPath}.relative_to`, opts);
	    const connection = def.connection;
	    if (Array.isArray(connection)) {
	        connection.forEach((rs, i) => remapRelativeSignal(rs, map, `${fieldPath}.connection[${i}]`, opts));
	    }
	}
	
	return signalIdRemapInternal;
}

var hasRequiredComponentFunctionDenormalized;

function requireComponentFunctionDenormalized () {
	if (hasRequiredComponentFunctionDenormalized) return componentFunctionDenormalized;
	hasRequiredComponentFunctionDenormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FunctionsNormalizedSchema = exports.FunctionScoreRowSchema = exports.ComponentFunctionDenormalizedSchema = exports.ComponentFunctionSignalDenormalizedSchema = exports.ALLOWED_RESOURCES = exports.ALLOWED_BUS_ROLES = exports.ALLOWED_FUNCTION_CLASSES = void 0;
		exports.convertComponentFunctionDenormalizedToIComponentFunction = convertComponentFunctionDenormalizedToIComponentFunction;
		exports.convertIComponentFunctionToComponentFunctionDenormalized = convertIComponentFunctionToComponentFunctionDenormalized;
		exports.convertFunctionsToScoreRows = convertFunctionsToScoreRows;
		exports.convertIComponentFunctionsToScoreRows = convertIComponentFunctionsToScoreRows;
		exports.remapSignalIdsInFunctions = remapSignalIdsInFunctions;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		exports.ALLOWED_FUNCTION_CLASSES = [
		    'bus.i2c.controller',
		    'bus.i2c.peripheral',
		    'bus.spi.controller',
		    'bus.spi.peripheral',
		];
		exports.ALLOWED_BUS_ROLES = ['controller', 'peripheral'];
		exports.ALLOWED_RESOURCES = ['i2c', 'spi'];
		exports.ComponentFunctionSignalDenormalizedSchema = zod_1.z.object({
		    name: zod_1.z.string().describe('The name of the function signal (e.g., "SCL", "SDA", "MOSI")'),
		    signalNames: zod_1.z.array(zod_1.z.string()).describe('Array of component signal names that this function signal maps to'),
		    busType: zod_1.z
		        .nativeEnum(sigma_engine_shared_1.DesignModels.ComponentFunctionBusType)
		        .nullable()
		        .describe('The type of bus if this is a communication bus signal'),
		    signalTypes: zod_1.z
		        .array(zod_1.z.nativeEnum(sigma_engine_shared_1.DesignModels.SignalDirection))
		        .nullable()
		        .describe('Array of allowed signal directions'),
		    optional: zod_1.z.boolean().describe('Whether this function signal is optional'),
		});
		exports.ComponentFunctionDenormalizedSchema = zod_1.z.object({
		    name: zod_1.z
		        .string()
		        .describe('The name of the function instance as it appears in the datasheet (e.g., "SPI1", "SPI2", "I2C1", "I2C2")'),
		    functionClass: zod_1.z.enum(exports.ALLOWED_FUNCTION_CLASSES).nullable().describe('The function class this function belongs to'),
		    busRole: zod_1.z.enum(exports.ALLOWED_BUS_ROLES).nullable().describe('The role of the function on the bus if applicable'),
		    mustBeAssigned: zod_1.z.boolean().describe('Whether this function must be assigned'),
		    resourceConsumed: zod_1.z.enum(exports.ALLOWED_RESOURCES).nullable().describe('The resource this function consumes'),
		    functionSignals: zod_1.z.array(exports.ComponentFunctionSignalDenormalizedSchema).describe('Array of function signals'),
		});
		function convertComponentFunctionDenormalizedToIComponentFunction(denormalized, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
		    const functionSignals = denormalized.functionSignals
		        .map((signal) => {
		        const functionSignal = {
		            name: signal.name,
		            signals: signal.signalNames.map((signalName) => ({ signal_name: signalName })),
		        };
		        if (signal.optional) {
		            functionSignal.optional = true;
		        }
		        if (signal.busType !== null) {
		            functionSignal.busType = signal.busType;
		        }
		        if (signal.signalTypes !== null && signal.signalTypes.length > 0) {
		            functionSignal.signalTypes = signal.signalTypes;
		        }
		        const parseResult = sigma_engine_shared_1.DesignModels.IComponentFunctionSignalSchema.safeParse(functionSignal);
		        if (!parseResult.success) {
		            logger.warn('ConvertComponentFunctionDenormalized_InvalidSignalDropped', {
		                functionName: denormalized.name,
		                signalName: signal.name,
		                errors: parseResult.error.flatten().fieldErrors,
		            });
		            return null;
		        }
		        return parseResult.data;
		    })
		        .filter((signal) => signal !== null);
		    const componentFunction = {
		        name: denormalized.name,
		        functionSignals,
		        mustBeAssigned: denormalized.mustBeAssigned,
		    };
		    if (denormalized.functionClass !== null) {
		        componentFunction.functionClass = denormalized.functionClass;
		    }
		    if (denormalized.busRole !== null) {
		        componentFunction.busRole = denormalized.busRole;
		    }
		    if (denormalized.resourceConsumed !== null) {
		        componentFunction.resourceConsumed = denormalized.resourceConsumed;
		    }
		    const parseResult = sigma_engine_shared_1.DesignModels.IComponentFunctionSchema.safeParse(componentFunction);
		    if (!parseResult.success) {
		        logger.warn('ConvertComponentFunctionDenormalized_InvalidFieldsDropped', {
		            functionName: denormalized.name,
		            errors: parseResult.error.flatten().fieldErrors,
		        });
		        return {
		            name: denormalized.name,
		            functionSignals,
		            mustBeAssigned: denormalized.mustBeAssigned,
		        };
		    }
		    return parseResult.data;
		}
		function lookupSignalName(signalId, signals) {
		    if (!signals) {
		        return String(signalId);
		    }
		    const matchingSignal = signals.find((s) => s.id === signalId);
		    return matchingSignal?.name ?? String(signalId);
		}
		function convertIComponentFunctionToComponentFunctionDenormalized(componentFunction, signals) {
		    const functionSignals = componentFunction.functionSignals.map((signal) => {
		        const signalNames = [];
		        for (const relativeSignal of signal.signals) {
		            if (relativeSignal.signal_name) {
		                if (Array.isArray(relativeSignal.signal_name)) {
		                    signalNames.push(...relativeSignal.signal_name);
		                }
		                else {
		                    signalNames.push(relativeSignal.signal_name);
		                }
		            }
		            else if (relativeSignal.signal) {
		                const signalIds = Array.isArray(relativeSignal.signal) ? relativeSignal.signal : [relativeSignal.signal];
		                signalNames.push(...signalIds.map((id) => lookupSignalName(id, signals)));
		            }
		        }
		        return {
		            name: signal.name,
		            signalNames,
		            busType: signal.busType ?? null,
		            signalTypes: signal.signalTypes ?? null,
		            optional: signal.optional ?? false,
		        };
		    });
		    return {
		        name: componentFunction.name,
		        functionClass: componentFunction.functionClass ?? null,
		        busRole: componentFunction.busRole ?? null,
		        mustBeAssigned: componentFunction.mustBeAssigned ?? false,
		        resourceConsumed: componentFunction.resourceConsumed ?? null,
		        functionSignals,
		    };
		}
		exports.FunctionScoreRowSchema = zod_1.z.object({
		    functionName: zod_1.z.string(),
		    functionClass: zod_1.z.enum(exports.ALLOWED_FUNCTION_CLASSES).nullable(),
		    busRole: zod_1.z.enum(exports.ALLOWED_BUS_ROLES).nullable(),
		    resourceConsumed: zod_1.z.enum(exports.ALLOWED_RESOURCES).nullable(),
		    functionSignalName: zod_1.z.string(),
		    signalNames: zod_1.z.string(),
		    optional: zod_1.z.boolean(),
		});
		exports.FunctionsNormalizedSchema = zod_1.z.object({
		    functions: zod_1.z.array(exports.FunctionScoreRowSchema),
		});
		function convertFunctionsToScoreRows(functions) {
		    return functions.flatMap((func) => func.functionSignals.map((signal) => ({
		        functionName: func.name,
		        functionClass: func.functionClass,
		        busRole: func.busRole,
		        resourceConsumed: func.resourceConsumed,
		        functionSignalName: signal.name,
		        signalNames: signal.signalNames.sort().join(','),
		        optional: signal.optional,
		    })));
		}
		function convertIComponentFunctionsToScoreRows(functions, signals) {
		    const denormalized = functions.map((f) => convertIComponentFunctionToComponentFunctionDenormalized(f, signals));
		    return convertFunctionsToScoreRows(denormalized);
		}
		const signal_id_remap_internal_1 = requireSignalIdRemapInternal();
		function remapSignalIdsInFunctions(functions, signalIdMap, opts) {
		    if (functions.length === 0) {
		        return [];
		    }
		    const cloned = JSON.parse(JSON.stringify(functions));
		    cloned.forEach((fn, i) => {
		        const fss = fn.functionSignals ?? [];
		        fss.forEach((fs, fi) => {
		            const sigs = fs.signals ?? [];
		            sigs.forEach((rs, si) => {
		                (0, signal_id_remap_internal_1.remapRelativeSignal)(rs, signalIdMap, `functions[${i}].functionSignals[${fi}].signals[${si}]`, opts);
		            });
		        });
		    });
		    return cloned;
		}
		
	} (componentFunctionDenormalized));
	return componentFunctionDenormalized;
}

var hasRequiredFunctions_comparison;

function requireFunctions_comparison () {
	if (hasRequiredFunctions_comparison) return functions_comparison;
	hasRequiredFunctions_comparison = 1;
	Object.defineProperty(functions_comparison, "__esModule", { value: true });
	functions_comparison.FunctionsComparison = functions_comparison.FunctionRowSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const component_function_denormalized_1 = requireComponentFunctionDenormalized();
	const comparison_spec_1 = requireComparisonSpec();
	functions_comparison.FunctionRowSchema = z.object({
	    name: z.string(),
	    functionClass: z.string(),
	    busRole: z.string(),
	    mustBeAssigned: z.boolean(),
	    resourceConsumed: z.string(),
	    functionSignals: component_function_denormalized_1.ComponentFunctionSignalDenormalizedSchema.array(),
	});
	function canonicalizeFunctionSignals(functionSignals) {
	    const canonical = functionSignals
	        .map((signal) => ({
	        name: signal.name,
	        busType: signal.busType ?? '',
	        signalTypes: signal.signalTypes === null ? '' : [...signal.signalTypes].sort().join(','),
	        optional: signal.optional,
	    }))
	        .sort((a, b) => a.name.localeCompare(b.name));
	    return JSON.stringify(canonical);
	}
	class FunctionsComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new comparison_spec_1.SetAlignment();
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return this.alignment.identityOf(row, (column, value, rowContext) => this.normalize(column, value, rowContext), Object.keys(row).sort());
	    }
	    normalize(column, value, row) {
	        if (column === 'functionSignals' && Array.isArray(value)) {
	            return canonicalizeFunctionSignals(value);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	functions_comparison.FunctionsComparison = FunctionsComparison;
	
	return functions_comparison;
}

var modes_comparison = {};

var hasRequiredModes_comparison;

function requireModes_comparison () {
	if (hasRequiredModes_comparison) return modes_comparison;
	hasRequiredModes_comparison = 1;
	Object.defineProperty(modes_comparison, "__esModule", { value: true });
	modes_comparison.ModesComparison = modes_comparison.ModeRowSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const comparison_spec_1 = requireComparisonSpec();
	modes_comparison.ModeRowSchema = z.object({
	    id: z.string(),
	    characteristic: z.string(),
	    minVal: z.number().nullable(),
	    nominalVal: z.number().nullable(),
	    maxVal: z.number().nullable(),
	    targetSignal: z.string(),
	    relativeSignal: z.string(),
	    referencedBy: z.string(),
	});
	const IDENTITY_COLUMNS = [
	    'characteristic',
	    'maxVal',
	    'minVal',
	    'nominalVal',
	    'relativeSignal',
	    'targetSignal',
	];
	class ModeContentIdentityAlignment extends comparison_spec_1.SetAlignment {
	    identityOf(row, normalize) {
	        return JSON.stringify(IDENTITY_COLUMNS.map((column) => [column, normalize(column, row[column], row)]));
	    }
	}
	class ModesComparison extends comparison_spec_1.ComparisonSpec {
	    constructor() {
	        super(...arguments);
	        this.alignment = new ModeContentIdentityAlignment();
	    }
	    toRows(data) {
	        return data;
	    }
	    fromRows(rows) {
	        return rows;
	    }
	    rowKeyOf(row) {
	        return this.alignment.identityOf(row, (column, value, rowContext) => this.normalize(column, value, rowContext));
	    }
	    normalize(column, value, row) {
	        if ((column === 'targetSignal' || column === 'relativeSignal') && typeof value === 'string') {
	            return (0, signal_name_normalization_1.normalizeSignalNameForComparison)(value);
	        }
	        return super.normalize(column, value, row);
	    }
	}
	modes_comparison.ModesComparison = ModesComparison;
	
	return modes_comparison;
}

var sliceContracts = {};

var hasRequiredSliceContracts;

function requireSliceContracts () {
	if (hasRequiredSliceContracts) return sliceContracts;
	hasRequiredSliceContracts = 1;
	Object.defineProperty(sliceContracts, "__esModule", { value: true });
	sliceContracts.SliceMaskPolicy = sliceContracts.ELEMENT_PARAMETERS_SLICE_KEY = sliceContracts.PARAMETERS_SLICE_KEY = sliceContracts.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY = sliceContracts.SIGMA_MODEL_TYPE_SLICE_KEY = void 0;
	sliceContracts.firstSigmaSubModel = firstSigmaSubModel;
	sliceContracts.readElementSigmaModelType = readElementSigmaModelType;
	const sigma_engine_shared_1 = requireDist$1();
	const slice_key_1 = requireSliceKey();
	sliceContracts.SIGMA_MODEL_TYPE_SLICE_KEY = slice_key_1.SliceKey.SigmaModelType;
	sliceContracts.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY = slice_key_1.SliceKey.ElementSigmaModelType;
	sliceContracts.PARAMETERS_SLICE_KEY = slice_key_1.SliceKey.Parameters;
	sliceContracts.ELEMENT_PARAMETERS_SLICE_KEY = slice_key_1.SliceKey.ElementParameters;
	var SliceMaskPolicy;
	(function (SliceMaskPolicy) {
	    SliceMaskPolicy["None"] = "none";
	    SliceMaskPolicy["ModelType"] = "model_type";
	    SliceMaskPolicy["ElementType"] = "element_type";
	})(SliceMaskPolicy || (sliceContracts.SliceMaskPolicy = SliceMaskPolicy = {}));
	function firstSigmaSubModel(payload) {
	    return payload.spiceModels?.find(sigma_engine_shared_1.SPICE.isSubModelSigmaSubModel);
	}
	function readElementSigmaModelType(payload) {
	    return firstSigmaSubModel(payload)?.modelId ?? '';
	}
	
	return sliceContracts;
}

var sigmaModelFormSlices = {};

var arraySubmodels = {};

var hasRequiredArraySubmodels;

function requireArraySubmodels () {
	if (hasRequiredArraySubmodels) return arraySubmodels;
	hasRequiredArraySubmodels = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ARRAY_PARENT_SIGMA_MODEL_TYPES = void 0;
		exports.parseSubModelId = parseSubModelId;
		exports.isArrayParentSigmaModel = isArrayParentSigmaModel;
		exports.getArrayElementConfig = getArrayElementConfig;
		exports.arrayParentForElement = arrayParentForElement;
		exports.composeArraySignalName = composeArraySignalName;
		exports.buildArraySubModels = buildArraySubModels;
		exports.extractArraySubmodelPrefix = extractArraySubmodelPrefix;
		const SUBMODEL_ID_PATTERN = /^([A-Z]+)[-_]?(\d+)$/;
		function parseSubModelId(subModelId) {
		    const match = SUBMODEL_ID_PATTERN.exec(subModelId);
		    if (match?.[1] === undefined || match[2] === undefined) {
		        return null;
		    }
		    return { prefix: match[1], ordinal: Number.parseInt(match[2], 10) };
		}
		exports.ARRAY_PARENT_SIGMA_MODEL_TYPES = {
		    passiveArray: 'passive.array',
		    icGeneric: 'ic.generic',
		};
		function isArrayParentSigmaModel(sigmaModelType) {
		    return Object.values(exports.ARRAY_PARENT_SIGMA_MODEL_TYPES).includes(sigmaModelType);
		}
		function getArrayElementConfig(elementModelType) {
		    switch (elementModelType) {
		        case 'resistor.standard':
		        case 'resistor.variable':
		            return { prefix: 'R', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'resistor.thermistor':
		            return { prefix: 'RT', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'resistor.varistor':
		            return { prefix: 'RV', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'capacitor.nonpolarized':
		        case 'capacitor.polarized':
		            return { prefix: 'C', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'diode.generic':
		        case 'diode.zener':
		        case 'diode.led':
		        case 'diode.tvs.bidirectional':
		        case 'diode.tvs.unidirectional':
		            return { prefix: 'D', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'ferrite.standard':
		            return { prefix: 'FB', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'fuse.generic':
		        case 'fuse.poly':
		            return { prefix: 'F', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'inductor.standard':
		            return { prefix: 'L', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.passiveArray };
		        case 'mosfet.enhancement.ntype':
		        case 'mosfet.enhancement.ptype':
		        case 'transistor.bjt.npn':
		        case 'transistor.bjt.pnp':
		            return { prefix: 'Q', parentType: exports.ARRAY_PARENT_SIGMA_MODEL_TYPES.icGeneric };
		        default:
		            return null;
		    }
		}
		function arrayParentForElement(elementModelType) {
		    return getArrayElementConfig(elementModelType)?.parentType ?? null;
		}
		function composeArraySignalName(submodelId, signalName) {
		    return `${submodelId}_${signalName}`;
		}
		function buildArraySignalConnectionMap(submodelId, elementModelSignals, signalsByName) {
		    const connectionMap = [];
		    for (const elementSignal of elementModelSignals) {
		        const compositeSignal = signalsByName.get(composeArraySignalName(submodelId, elementSignal.name));
		        if (compositeSignal === undefined) {
		            continue;
		        }
		        connectionMap.push({
		            componentSignalId: String(compositeSignal.id),
		            modelSignalId: elementSignal.id,
		        });
		    }
		    return connectionMap;
		}
		function buildArraySubModels(args) {
		    const signalsByName = new Map(args.signals.map((signal) => [signal.name, { id: signal.id, name: signal.name }]));
		    const parameterMap = args.parameterMap ?? {};
		    const submodels = [];
		    for (let index = 1; index <= args.elementCount; index += 1) {
		        const subModelId = `${args.submodelPrefix}${index}`;
		        submodels.push({
		            type: 'sigma',
		            subModelId,
		            modelId: args.elementModelId,
		            modelInfo: {
		                signalConnectionMap: buildArraySignalConnectionMap(subModelId, args.elementModelSignals, signalsByName),
		                parameterMap: { ...parameterMap },
		            },
		        });
		    }
		    return submodels;
		}
		function extractArraySubmodelPrefix(submodelIds) {
		    if (submodelIds.length === 0) {
		        return { isValid: false, reason: 'No submodel IDs provided' };
		    }
		    const parsed = [];
		    const invalidIds = [];
		    for (const submodelId of submodelIds) {
		        const ref = parseSubModelId(submodelId);
		        if (ref === null) {
		            invalidIds.push(submodelId);
		            continue;
		        }
		        parsed.push({ prefix: ref.prefix, index: ref.ordinal });
		    }
		    if (invalidIds.length > 0) {
		        return { isValid: false, reason: `Invalid submodel ID format: ${invalidIds.join(', ')}` };
		    }
		    const prefixes = new Set(parsed.map((entry) => entry.prefix));
		    if (prefixes.size !== 1) {
		        return { isValid: false, reason: `Mixed submodel prefixes found: ${[...prefixes].join(', ')}` };
		    }
		    const sortedIndices = parsed.map((entry) => entry.index).sort((left, right) => left - right);
		    if (!sortedIndices.every((index, position) => index === position + 1)) {
		        return {
		            isValid: false,
		            reason: `Submodel numbers are not sequential starting from 1: ${sortedIndices.join(', ')}`,
		        };
		    }
		    const [prefix] = [...prefixes];
		    return { isValid: true, prefix };
		}
		
	} (arraySubmodels));
	return arraySubmodels;
}

var arrayResolutionAdapter = {};

var hasRequiredArrayResolutionAdapter;

function requireArrayResolutionAdapter () {
	if (hasRequiredArrayResolutionAdapter) return arrayResolutionAdapter;
	hasRequiredArrayResolutionAdapter = 1;
	Object.defineProperty(arrayResolutionAdapter, "__esModule", { value: true });
	arrayResolutionAdapter.parseArrayParameterMap = parseArrayParameterMap;
	arrayResolutionAdapter.readArrayFormFromResolution = readArrayFormFromResolution;
	arrayResolutionAdapter.readArrayFormFromSubModels = readArrayFormFromSubModels;
	arrayResolutionAdapter.readArrayElementParametersFromSubModels = readArrayElementParametersFromSubModels;
	arrayResolutionAdapter.arrayParentTypeForElement = arrayParentTypeForElement;
	arrayResolutionAdapter.buildArrayFormSpiceModels = buildArrayFormSpiceModels;
	const sigma_engine_shared_1 = requireDist$1();
	const array_submodels_1 = requireArraySubmodels();
	function parseArrayParameterMap(parameters, parameterDefs) {
	    const unitsByName = new Map(parameterDefs.map((def) => [def.name, def.units ?? {}]));
	    const parameterMap = {};
	    for (const [name, entry] of Object.entries(parameters)) {
	        const raw = entry?.value?.trim() ?? '';
	        if (raw === '') {
	            continue;
	        }
	        try {
	            const parsed = sigma_engine_shared_1.Utils.parseNumericalSIValue(raw, unitsByName.get(name) ?? {});
	            if (!Number.isNaN(parsed)) {
	                parameterMap[name] = parsed;
	            }
	        }
	        catch {
	        }
	    }
	    return parameterMap;
	}
	function readArrayFormFromResolution(resolution) {
	    if (!(0, array_submodels_1.isArrayParentSigmaModel)(resolution.sigmaModelId)) {
	        return null;
	    }
	    if (resolution.spiceModels === undefined) {
	        return null;
	    }
	    const submodels = resolution.spiceModels.filter(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel);
	    if (submodels.length === 0) {
	        return null;
	    }
	    const prefixResult = (0, array_submodels_1.extractArraySubmodelPrefix)(submodels.map((submodel) => submodel.subModelId));
	    if (!prefixResult.isValid || prefixResult.prefix === undefined) {
	        return null;
	    }
	    const elementModelIds = new Set(submodels.map((submodel) => submodel.modelId));
	    if (elementModelIds.size !== 1) {
	        return null;
	    }
	    const [elementModelType] = [...elementModelIds];
	    if (elementModelType === undefined) {
	        return null;
	    }
	    const config = (0, array_submodels_1.getArrayElementConfig)(elementModelType);
	    if (config === null || config.parentType !== resolution.sigmaModelId) {
	        return null;
	    }
	    return {
	        confirmed: true,
	        elementModelType,
	        elementCount: submodels.length,
	        submodelPrefix: prefixResult.prefix,
	    };
	}
	function readArrayFormFromSubModels(spiceModels) {
	    const submodel = spiceModels?.find(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel);
	    if (submodel === undefined) {
	        return null;
	    }
	    const config = (0, array_submodels_1.getArrayElementConfig)(submodel.modelId);
	    if (config === null) {
	        return null;
	    }
	    return readArrayFormFromResolution({ sigmaModelId: config.parentType, spiceModels });
	}
	function readArrayElementParametersFromSubModels(spiceModels) {
	    const [submodel] = (spiceModels ?? [])
	        .filter(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel)
	        .sort((left, right) => left.subModelId.localeCompare(right.subModelId));
	    if (submodel === undefined) {
	        return {};
	    }
	    const parameters = {};
	    for (const [key, value] of Object.entries(submodel.modelInfo.parameterMap ?? {})) {
	        parameters[key] = { value: String(value), variance: null };
	    }
	    return parameters;
	}
	function arrayParentTypeForElement(elementModelType) {
	    return (0, array_submodels_1.getArrayElementConfig)(elementModelType)?.parentType ?? null;
	}
	function buildArrayFormSpiceModels(args) {
	    if (!args.arrayForm.confirmed) {
	        return [];
	    }
	    return (0, array_submodels_1.buildArraySubModels)({
	        elementModelId: args.arrayForm.elementModelType,
	        elementCount: args.arrayForm.elementCount,
	        submodelPrefix: args.arrayForm.submodelPrefix,
	        signals: args.signals.map((signal) => ({ id: signal.id, name: signal.name })),
	        elementModelSignals: args.elementSignals,
	        parameterMap: parseArrayParameterMap(args.parameters, args.parameterDefs),
	    });
	}
	
	return arrayResolutionAdapter;
}

var formLatest$2 = {};

var misc = {};

var hasRequiredMisc;

function requireMisc () {
	if (hasRequiredMisc) return misc;
	hasRequiredMisc = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.reconcilePackagesPackageSchema = exports.pkgFunctionsSchema = exports.pkgPinoutSchema = exports.pkgParametersSchema = exports.sequenceGateSchema = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.sequenceGateSchema = zod_1.z.unknown().optional();
		exports.pkgParametersSchema = zod_1.z.record(zod_1.z.string(), zod_1.z
		    .object({
		    value: zod_1.z.string().nullable(),
		    variance: zod_1.z.string().nullable(),
		    notSpecified: zod_1.z.boolean().optional(),
		})
		    .transform((entry) => (entry.notSpecified === true ? { ...entry, value: null, variance: null } : entry)));
		exports.pkgPinoutSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.string().array());
		exports.pkgFunctionsSchema = sigma_engine_shared_1.IComponentFunctionSchema.array();
		exports.reconcilePackagesPackageSchema = zod_1.z.object({
		    boundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
		    functions: exports.pkgFunctionsSchema,
		    modes: sigma_engine_shared_1.ModeBaseSchema.array(),
		    parameters: exports.pkgParametersSchema,
		    pinout: exports.pkgPinoutSchema,
		    resources: sigma_engine_shared_1.IComponentResourceSchema.array(),
		    sigmaModelType: zod_1.z.string(),
		    signals: sigma_engine_shared_1.SignalSchema.array(),
		});
		
	} (misc));
	return misc;
}

var formModelShape = {};

var hasRequiredFormModelShape;

function requireFormModelShape () {
	if (hasRequiredFormModelShape) return formModelShape;
	hasRequiredFormModelShape = 1;
	Object.defineProperty(formModelShape, "__esModule", { value: true });
	formModelShape.isFloatingBehaviorOperatorSet = isFloatingBehaviorOperatorSet;
	formModelShape.modelFloatingBehavior = modelFloatingBehavior;
	formModelShape.resolveFormFloatingBehavior = resolveFormFloatingBehavior;
	formModelShape.recoverFloatingBehaviorChoice = recoverFloatingBehaviorChoice;
	formModelShape.buildCanonicalSignalFromModelSignal = buildCanonicalSignalFromModelSignal;
	formModelShape.buildUnboundCanonicalSignal = buildUnboundCanonicalSignal;
	formModelShape.floatingBehaviorField = floatingBehaviorField;
	const sigma_engine_shared_1 = requireDist$1();
	function isFloatingBehaviorOperatorSet(modelSignal) {
	    return modelSignal === undefined || modelSignal.isOptional;
	}
	function modelFloatingBehavior(modelSignal) {
	    return modelSignal.floatingBehavior ?? sigma_engine_shared_1.DesignModels.SignalFloatingBehavior.Never;
	}
	function resolveFormFloatingBehavior(modelSignal, choice) {
	    if (choice !== undefined || modelSignal === undefined || isFloatingBehaviorOperatorSet(modelSignal)) {
	        return choice;
	    }
	    return modelFloatingBehavior(modelSignal);
	}
	function recoverFloatingBehaviorChoice(modelSignal, stored) {
	    const unchosen = modelSignal === undefined || isFloatingBehaviorOperatorSet(modelSignal) ?
	        sigma_engine_shared_1.DesignModels.SignalFloatingBehavior.Unspecified
	        : modelFloatingBehavior(modelSignal);
	    return stored === unchosen ? undefined : stored;
	}
	function buildCanonicalSignalFromModelSignal(modelSignal, overrides = {}) {
	    return {
	        id: overrides.id ?? modelSignal.id,
	        name: overrides.name ?? modelSignal.name,
	        padRefs: [],
	        pinRefs: [],
	        signalDirection: modelSignal.direction ?? undefined,
	        attributes: { ...modelSignal.attributes },
	        description: modelSignal.description ?? undefined,
	        modelSignal: modelSignal.id,
	        blockInfo: overrides.blockInfo,
	        ...floatingBehaviorField(resolveFormFloatingBehavior(modelSignal, overrides.floatingBehavior)),
	        supportedDriveModes: [],
	        ties: [],
	        isCadstromInternal: false,
	    };
	}
	function buildUnboundCanonicalSignal(args) {
	    return {
	        id: args.id,
	        name: args.name,
	        padRefs: [],
	        pinRefs: [],
	        attributes: {},
	        ...(args.blockInfo !== undefined ? { blockInfo: args.blockInfo } : {}),
	        ...floatingBehaviorField(args.floatingBehavior),
	        supportedDriveModes: [],
	        ties: [],
	        isCadstromInternal: false,
	    };
	}
	function floatingBehaviorField(floatingBehavior) {
	    return floatingBehavior !== undefined ? { floatingBehavior } : {};
	}
	
	return formModelShape;
}

var noConnectSignal = {};

var hasRequiredNoConnectSignal;

function requireNoConnectSignal () {
	if (hasRequiredNoConnectSignal) return noConnectSignal;
	hasRequiredNoConnectSignal = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NO_CONNECT_SIGNAL_ID = exports.NO_CONNECT_SIGNAL_NAME = void 0;
		exports.isNoConnectSignal = isNoConnectSignal;
		exports.buildNoConnectSignal = buildNoConnectSignal;
		exports.readNoConnectPinsFromPayload = readNoConnectPinsFromPayload;
		exports.readNoConnectFloatingBehaviorFromPayload = readNoConnectFloatingBehaviorFromPayload;
		const CadstromUid_1 = requireCadstromUid();
		const generate_readable_id_1 = requireGenerateReadableId();
		const form_model_shape_1 = requireFormModelShape();
		exports.NO_CONNECT_SIGNAL_NAME = 'NC';
		exports.NO_CONNECT_SIGNAL_ID = (0, generate_readable_id_1.generateReadableId)(CadstromUid_1.CadstromObjectType.Signal, exports.NO_CONNECT_SIGNAL_NAME, 0);
		function isNoConnectSignal(signal) {
		    return signal.attributes?.noConnect === true;
		}
		function buildNoConnectSignal(floatingBehavior) {
		    return {
		        id: exports.NO_CONNECT_SIGNAL_ID,
		        name: exports.NO_CONNECT_SIGNAL_NAME,
		        padRefs: [],
		        pinRefs: [],
		        attributes: { noConnect: true },
		        ...(0, form_model_shape_1.floatingBehaviorField)(floatingBehavior),
		    };
		}
		function readNoConnectPinsFromPayload(payload) {
		    const ncSignal = payload.signals.find(isNoConnectSignal);
		    if (ncSignal === undefined) {
		        return [];
		    }
		    return [...(payload.pinout[String(ncSignal.id)] ?? [])];
		}
		function readNoConnectFloatingBehaviorFromPayload(payload) {
		    return (0, form_model_shape_1.recoverFloatingBehaviorChoice)(undefined, payload.signals.find(isNoConnectSignal)?.floatingBehavior);
		}
		
	} (noConnectSignal));
	return noConnectSignal;
}

var pinCountRules = {};

var hasRequiredPinCountRules;

function requirePinCountRules () {
	if (hasRequiredPinCountRules) return pinCountRules;
	hasRequiredPinCountRules = 1;
	Object.defineProperty(pinCountRules, "__esModule", { value: true });
	pinCountRules.normalizePinDesignators = normalizePinDesignators;
	pinCountRules.derivePhysicalPinCount = derivePhysicalPinCount;
	pinCountRules.inferPinCountFromDefinition = inferPinCountFromDefinition;
	pinCountRules.resolveSeedPinCount = resolveSeedPinCount;
	function normalizePinDesignators(pins) {
	    return pins.map((pin) => pin.trim()).filter((pin) => pin !== '');
	}
	function derivePhysicalPinCount(pinout, additionalPins = []) {
	    const uniquePins = new Set();
	    for (const pins of Object.values(pinout)) {
	        for (const designator of normalizePinDesignators(pins)) {
	            uniquePins.add(designator);
	        }
	    }
	    for (const designator of normalizePinDesignators(additionalPins)) {
	        uniquePins.add(designator);
	    }
	    return uniquePins.size < 2 ? null : uniquePins.size;
	}
	function inferPinCountFromDefinition(definition) {
	    if (definition === null) {
	        return null;
	    }
	    if (definition.repeatableBlocks.length > 0) {
	        return null;
	    }
	    if (definition.signals.some((signal) => signal.isOptional)) {
	        return null;
	    }
	    if (definition.signals.length < 2) {
	        return null;
	    }
	    return definition.signals.length;
	}
	function resolveSeedPinCount(definition, pinout, noConnectPins = []) {
	    const inferred = inferPinCountFromDefinition(definition);
	    const derived = derivePhysicalPinCount(pinout, noConnectPins);
	    if (inferred === null) {
	        return derived;
	    }
	    if (derived === null) {
	        return inferred;
	    }
	    return Math.max(inferred, derived);
	}
	
	return pinCountRules;
}

var hasRequiredFormLatest$2;

function requireFormLatest$2 () {
	if (hasRequiredFormLatest$2) return formLatest$2;
	hasRequiredFormLatest$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.sigmaModelEntryFormLatest = exports.SigmaModelEntryFormLatest = exports.SIGMA_MODEL_ENTRY_FORM_STATE_CODEC = exports.UNBOUND_MODEL_SIGNAL_ID = exports.SigmaModelEntryFormDataLatestSchema = exports.SigmaModelEntryFormStateSchema = exports.FormPinAssignmentSchema = exports.PinBlockRefSchema = exports.SigmaModelEntryFormStateCodec = exports.BoundSigmaModelEntryForm = exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION = void 0;
		exports.isUnboundPinAssignment = isUnboundPinAssignment;
		exports.flagOperatorAddedPinAssignments = flagOperatorAddedPinAssignments;
		exports.payloadToFormState = payloadToFormState;
		exports.formStateToCanonical = formStateToCanonical;
		exports.payloadToSimpleFormState = payloadToSimpleFormState;
		exports.simpleFormStateToCanonical = simpleFormStateToCanonical;
		exports.payloadToRepeatableBlockFormState = payloadToRepeatableBlockFormState;
		exports.repeatableBlockFormStateToCanonical = repeatableBlockFormStateToCanonical;
		exports.payloadToArrayFormState = payloadToArrayFormState;
		exports.arrayFormStateToCanonical = arrayFormStateToCanonical;
		exports.blockRefFor = blockRefFor;
		exports.buildInitialFormState = buildInitialFormState;
		exports.remapSeedPinoutForModelSignals = remapSeedPinoutForModelSignals;
		const sigma_engine_shared_1 = requireDist$1();
		const z = /*@__PURE__*/ requireZod();
		const CadstromUid_1 = requireCadstromUid();
		const generate_readable_id_1 = requireGenerateReadableId();
		const array_submodels_1 = requireArraySubmodels();
		const misc_1 = requireMisc();
		const array_resolution_adapter_1 = requireArrayResolutionAdapter();
		const form_model_shape_1 = requireFormModelShape();
		const no_connect_signal_1 = requireNoConnectSignal();
		const pin_count_rules_1 = requirePinCountRules();
		exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION = 'sigma-model-entry-form/v2';
		class BoundSigmaModelEntryForm {
		    constructor(resolver) {
		        this.resolver = resolver;
		    }
		    toFormData(payload) {
		        return payloadToFormState(payload, this.resolver);
		    }
		    toCanonical(formState) {
		        return formStateToCanonical(formState, this.resolver);
		    }
		    initForClass(sigmaModelType, seed = null) {
		        return buildInitialFormState(sigmaModelType, this.resolver, seed);
		    }
		}
		exports.BoundSigmaModelEntryForm = BoundSigmaModelEntryForm;
		class SigmaModelEntryFormStateCodec {
		    constructor() {
		        this.version = exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION;
		        this.formDataSchema = exports.SigmaModelEntryFormStateSchema;
		    }
		    bind(resolver) {
		        return new BoundSigmaModelEntryForm(resolver);
		    }
		}
		exports.SigmaModelEntryFormStateCodec = SigmaModelEntryFormStateCodec;
		exports.SigmaModelEntryFormLatest = SigmaModelEntryFormStateCodec;
		exports.PinBlockRefSchema = z.object({
		    name: z.string().min(1),
		    index: z.number().int().nonnegative(),
		});
		exports.FormPinAssignmentSchema = z.object({
		    modelSignalId: z.string(),
		    block: exports.PinBlockRefSchema.nullable(),
		    nameOverride: z.string().nullable(),
		    pins: z.string().array(),
		    packageSignalId: z.string().optional(),
		    floatingBehavior: sigma_engine_shared_1.DesignModels.SignalFloatingBehaviorSchema.optional(),
		});
		exports.SigmaModelEntryFormStateSchema = z.object({
		    formVersion: z.literal(exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION),
		    parentSigmaModelType: z.string(),
		    elementSigmaModelType: z.string().nullable(),
		    pinCount: z.number().int().nullable(),
		    arrayConfirmed: z.boolean().nullable(),
		    pinAssignments: exports.FormPinAssignmentSchema.array(),
		    ncPins: z.string().array(),
		    ncFloatingBehavior: sigma_engine_shared_1.DesignModels.SignalFloatingBehaviorSchema.optional(),
		    parameters: misc_1.pkgParametersSchema,
		});
		exports.SigmaModelEntryFormDataLatestSchema = exports.SigmaModelEntryFormStateSchema;
		exports.UNBOUND_MODEL_SIGNAL_ID = '';
		function isUnboundPinAssignment(assignment) {
		    return assignment.modelSignalId === exports.UNBOUND_MODEL_SIGNAL_ID;
		}
		function flagOperatorAddedPinAssignments(assignments) {
		    const seen = new Set();
		    return assignments.map((assignment) => {
		        if (isUnboundPinAssignment(assignment)) {
		            return true;
		        }
		        const key = assignment.block === null ?
		            assignment.modelSignalId
		            : `${assignment.block.name}:${assignment.block.index}:${assignment.modelSignalId}`;
		        if (seen.has(key)) {
		            return true;
		        }
		        seen.add(key);
		        return false;
		    });
		}
		function payloadToFormState(payload, resolver) {
		    const arrayForm = (0, array_resolution_adapter_1.readArrayFormFromResolution)({
		        sigmaModelId: payload.sigmaModelType,
		        spiceModels: payload.spiceModels,
		    });
		    if (arrayForm !== null) {
		        return payloadToArrayFormState(payload, resolver);
		    }
		    const definition = resolver(payload.sigmaModelType);
		    if (definition !== null && definition.repeatableBlocks.length > 0) {
		        return payloadToRepeatableBlockFormState(payload, definition);
		    }
		    return payloadToSimpleFormState(payload, definition);
		}
		function formStateToCanonical(formState, resolver) {
		    if (formState.elementSigmaModelType !== null) {
		        return arrayFormStateToCanonical(formState, resolver);
		    }
		    const definition = resolver(formState.parentSigmaModelType);
		    if (definition !== null && definition.repeatableBlocks.length > 0) {
		        return repeatableBlockFormStateToCanonical(formState, definition);
		    }
		    return simpleFormStateToCanonical(formState, definition);
		}
		function payloadToSimpleFormState(payload, definition) {
		    const pinAssignments = definition !== null ?
		        seedSimpleAssignments(payload, definition)
		        : payload.signals
		            .filter((signal) => !(0, no_connect_signal_1.isNoConnectSignal)(signal))
		            .map((signal) => {
		            const modelSignalId = modelSignalIdOf(signal);
		            return withFloatingBehaviorChoice({
		                modelSignalId,
		                block: null,
		                nameOverride: null,
		                pins: [...(payload.pinout[modelSignalId] ?? payload.pinout[String(signal.id)] ?? [])],
		            }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(undefined, signal.floatingBehavior));
		        });
		    return {
		        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
		        parentSigmaModelType: payload.sigmaModelType,
		        elementSigmaModelType: null,
		        pinCount: (0, pin_count_rules_1.resolveSeedPinCount)(definition, payload.pinout),
		        arrayConfirmed: null,
		        pinAssignments,
		        ncPins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
		        ...noConnectFloatingBehaviorField((0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(payload)),
		        parameters: payload.parameters,
		    };
		}
		function seedSimpleAssignments(payload, definition) {
		    const modelSignalById = new Map(definition.signals.map((modelSignal) => [String(modelSignal.id), modelSignal]));
		    const storedSignalByModelSignalId = new Map();
		    const added = [];
		    for (const signal of payload.signals) {
		        if ((0, no_connect_signal_1.isNoConnectSignal)(signal)) {
		            continue;
		        }
		        const modelSignalId = modelSignalIdOf(signal);
		        const modelSignal = modelSignalById.get(modelSignalId);
		        if (modelSignal === undefined) {
		            if (!hasModelSignalRef(signal)) {
		                added.push(unboundAssignment(signal, null, payload.pinout));
		            }
		            continue;
		        }
		        if (!storedSignalByModelSignalId.has(modelSignalId)) {
		            storedSignalByModelSignalId.set(modelSignalId, signal);
		            continue;
		        }
		        added.push(withFloatingBehaviorChoice({
		            modelSignalId,
		            block: null,
		            nameOverride: signal.name,
		            pins: [...(payload.pinout[String(signal.id)] ?? [])],
		            packageSignalId: String(signal.id),
		        }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, signal.floatingBehavior)));
		    }
		    const declared = definition.signals.map((modelSignal) => {
		        const modelSignalId = String(modelSignal.id);
		        const storedSignal = storedSignalByModelSignalId.get(modelSignalId);
		        const nameOverride = storedSignal !== undefined && storedSignal.name !== modelSignal.name ? storedSignal.name : null;
		        const pins = (storedSignal !== undefined ? payload.pinout[String(storedSignal.id)] : undefined) ??
		            payload.pinout[modelSignalId] ??
		            [];
		        return withFloatingBehaviorChoice({ modelSignalId, block: null, nameOverride, pins: [...pins] }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, storedSignal?.floatingBehavior));
		    });
		    return [...declared, ...added];
		}
		function simpleFormStateToCanonical(formState, definition) {
		    const modelSignalById = new Map((definition?.signals ?? []).map((modelSignal) => [String(modelSignal.id), modelSignal]));
		    const added = flagOperatorAddedPinAssignments(formState.pinAssignments);
		    const signals = [];
		    const pinout = {};
		    for (const [index, assignment] of formState.pinAssignments.entries()) {
		        if (assignment.block !== null) {
		            continue;
		        }
		        const isAdded = added[index] === true;
		        const modelSignal = modelSignalById.get(assignment.modelSignalId);
		        const pins = (0, pin_count_rules_1.normalizePinDesignators)(assignment.pins);
		        if (isUnpinnedOptional(modelSignal, pins) || (isAdded && pins.length === 0)) {
		            continue;
		        }
		        if (isUnboundPinAssignment(assignment)) {
		            const signal = unboundCanonicalSignal(assignment);
		            signals.push(signal);
		            pinout[String(signal.id)] = pins;
		            continue;
		        }
		        const addedId = isAdded ?
		            (assignment.packageSignalId ??
		                freshSignalId(assignment.nameOverride ?? modelSignal?.name ?? assignment.modelSignalId))
		            : undefined;
		        signals.push(modelSignal !== undefined ?
		            (0, form_model_shape_1.buildCanonicalSignalFromModelSignal)(modelSignal, {
		                ...(addedId !== undefined ? { id: addedId } : {}),
		                name: assignment.nameOverride,
		                floatingBehavior: assignment.floatingBehavior,
		            })
		            : fallbackSignal(addedId ?? assignment.modelSignalId, assignment.nameOverride ?? assignment.modelSignalId, assignment.floatingBehavior));
		        pinout[addedId ?? assignment.modelSignalId] = pins;
		    }
		    appendNoConnectSignal(signals, pinout, formState.ncPins, formState.ncFloatingBehavior);
		    return {
		        sigmaModelType: formState.parentSigmaModelType,
		        parameters: formState.parameters,
		        signals,
		        pinout,
		        spiceModels: [],
		    };
		}
		function payloadToRepeatableBlockFormState(payload, definition) {
		    const modelSignalById = new Map((definition?.signals ?? []).map((modelSignal) => [String(modelSignal.id), modelSignal]));
		    const modelSignalOrder = new Map((definition?.signals ?? []).map((modelSignal, index) => [String(modelSignal.id), index]));
		    const groupOrder = [];
		    const entriesByGroup = new Map();
		    const blockByGroup = new Map();
		    for (const signal of payload.signals) {
		        if ((0, no_connect_signal_1.isNoConnectSignal)(signal)) {
		            continue;
		        }
		        const modelSignalId = modelSignalIdOf(signal);
		        const modelSignal = modelSignalById.get(modelSignalId);
		        const isUnbound = definition !== null && modelSignal === undefined && !hasModelSignalRef(signal);
		        if (definition !== null && modelSignal === undefined && !isUnbound) {
		            continue;
		        }
		        const block = blockRefOf(signal, modelSignal);
		        const groupKey = block !== null ? `${block.name}:${block.index}` : String(signal.id);
		        const assignment = isUnbound ?
		            unboundAssignment(signal, block, payload.pinout)
		            : withFloatingBehaviorChoice({
		                modelSignalId,
		                block,
		                nameOverride: modelSignal !== undefined && signal.name !== modelSignal.name ? signal.name : null,
		                pins: [...(payload.pinout[String(signal.id)] ?? [])],
		                packageSignalId: String(signal.id),
		            }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, signal.floatingBehavior));
		        const signalOrder = modelSignalOrder.get(modelSignalId) ?? Number.MAX_SAFE_INTEGER;
		        const existing = entriesByGroup.get(groupKey);
		        if (existing === undefined) {
		            groupOrder.push(groupKey);
		            entriesByGroup.set(groupKey, [{ assignment, signalOrder }]);
		            blockByGroup.set(groupKey, block);
		        }
		        else {
		            existing.push({ assignment, signalOrder });
		        }
		    }
		    const pinAssignments = [...groupOrder]
		        .sort((left, right) => compareRepeatableBlocks(blockByGroup.get(left) ?? null, blockByGroup.get(right) ?? null))
		        .flatMap((groupKey) => (entriesByGroup.get(groupKey) ?? [])
		        .sort((a, b) => a.signalOrder - b.signalOrder)
		        .map((entry) => entry.assignment));
		    return {
		        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
		        parentSigmaModelType: payload.sigmaModelType,
		        elementSigmaModelType: null,
		        pinCount: (0, pin_count_rules_1.resolveSeedPinCount)(definition, payload.pinout),
		        arrayConfirmed: null,
		        pinAssignments,
		        ncPins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
		        ...noConnectFloatingBehaviorField((0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(payload)),
		        parameters: payload.parameters,
		    };
		}
		function repeatableBlockFormStateToCanonical(formState, definition) {
		    const modelSignalById = new Map((definition?.signals ?? []).map((modelSignal) => [String(modelSignal.id), modelSignal]));
		    const added = flagOperatorAddedPinAssignments(formState.pinAssignments);
		    const signals = [];
		    const pinout = {};
		    for (const [index, assignment] of formState.pinAssignments.entries()) {
		        const modelSignal = modelSignalById.get(assignment.modelSignalId);
		        const pins = (0, pin_count_rules_1.normalizePinDesignators)(assignment.pins);
		        if (isUnpinnedOptional(modelSignal, pins) || (added[index] === true && pins.length === 0)) {
		            continue;
		        }
		        if (isUnboundPinAssignment(assignment)) {
		            const signal = unboundCanonicalSignal(assignment);
		            signals.push(signal);
		            pinout[String(signal.id)] = pins;
		            continue;
		        }
		        const effectiveName = assignment.nameOverride ?? modelSignal?.name ?? assignment.modelSignalId;
		        const blockInfo = assignment.block !== null && assignment.block.name !== 'pwr' ? { index: assignment.block.index } : undefined;
		        const id = assignment.packageSignalId ??
		            freshSignalId(assignment.block !== null && blockInfo !== undefined ?
		                (0, array_submodels_1.composeArraySignalName)(`${assignment.block.name}${String(assignment.block.index + 1)}`, effectiveName)
		                : effectiveName);
		        signals.push(modelSignal !== undefined ?
		            (0, form_model_shape_1.buildCanonicalSignalFromModelSignal)(modelSignal, {
		                name: assignment.nameOverride,
		                id,
		                blockInfo,
		                floatingBehavior: assignment.floatingBehavior,
		            })
		            : fallbackSignal(id, effectiveName, assignment.floatingBehavior));
		        pinout[id] = pins;
		    }
		    appendNoConnectSignal(signals, pinout, formState.ncPins, formState.ncFloatingBehavior);
		    return {
		        sigmaModelType: formState.parentSigmaModelType,
		        parameters: formState.parameters,
		        signals,
		        pinout,
		        spiceModels: [],
		    };
		}
		function payloadToArrayFormState(payload, resolver) {
		    const arrayForm = (0, array_resolution_adapter_1.readArrayFormFromResolution)({
		        sigmaModelId: payload.sigmaModelType,
		        spiceModels: payload.spiceModels,
		    });
		    const elementType = arrayForm?.elementModelType ?? payload.sigmaModelType;
		    const prefix = arrayForm?.submodelPrefix ?? (0, array_submodels_1.getArrayElementConfig)(elementType)?.prefix ?? '';
		    const elementCount = arrayForm?.elementCount ?? 0;
		    const elementDefinition = resolver(elementType);
		    const signalById = new Map(payload.signals.map((signal) => [String(signal.id), signal]));
		    const connectionByKey = new Map();
		    for (const submodel of payload.spiceModels.filter(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel)) {
		        const index = parseSubModelIndex(submodel.subModelId);
		        for (const connection of submodel.modelInfo.signalConnectionMap) {
		            connectionByKey.set(`${index}:${String(connection.modelSignalId)}`, {
		                packageSignalId: String(connection.componentSignalId),
		                pins: [...(payload.pinout[String(connection.componentSignalId)] ?? [])],
		            });
		        }
		    }
		    const connectedSignalIds = new Set([...connectionByKey.values()].map((connection) => connection.packageSignalId));
		    const unboundByElement = new Map();
		    for (const signal of payload.signals) {
		        if ((0, no_connect_signal_1.isNoConnectSignal)(signal) || connectedSignalIds.has(String(signal.id))) {
		            continue;
		        }
		        const index = signal.blockInfo?.index ?? 0;
		        const assignment = unboundAssignment(signal, { name: prefix, index }, payload.pinout);
		        unboundByElement.set(index, [...(unboundByElement.get(index) ?? []), assignment]);
		    }
		    const pinAssignments = [];
		    const terminals = elementDefinition?.signals ?? [];
		    if (terminals.length > 0) {
		        for (let index = 0; index < elementCount; index += 1) {
		            for (const terminal of terminals) {
		                const connection = connectionByKey.get(`${index}:${String(terminal.id)}`);
		                const submodelId = `${prefix}${index + 1}`;
		                const structuralName = (0, array_submodels_1.composeArraySignalName)(submodelId, terminal.name);
		                const storedSignal = connection !== undefined ? signalById.get(connection.packageSignalId) : undefined;
		                const nameOverride = storedSignal !== undefined && storedSignal.name !== structuralName ? storedSignal.name : null;
		                pinAssignments.push(withFloatingBehaviorChoice({
		                    modelSignalId: String(terminal.id),
		                    block: { name: prefix, index },
		                    nameOverride,
		                    pins: connection?.pins ?? [],
		                    ...(connection !== undefined ? { packageSignalId: connection.packageSignalId } : {}),
		                }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(terminal, storedSignal?.floatingBehavior)));
		            }
		            pinAssignments.push(...(unboundByElement.get(index) ?? []));
		            unboundByElement.delete(index);
		        }
		    }
		    else {
		        for (const [key, connection] of connectionByKey) {
		            const [indexText, modelSignalId] = key.split(':');
		            pinAssignments.push({
		                modelSignalId: modelSignalId ?? '',
		                block: { name: prefix, index: Number.parseInt(indexText ?? '0', 10) },
		                nameOverride: null,
		                pins: connection.pins,
		                packageSignalId: connection.packageSignalId,
		            });
		        }
		    }
		    for (const index of [...unboundByElement.keys()].sort((left, right) => left - right)) {
		        pinAssignments.push(...(unboundByElement.get(index) ?? []));
		    }
		    return {
		        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
		        parentSigmaModelType: payload.sigmaModelType,
		        elementSigmaModelType: elementType,
		        pinCount: (0, pin_count_rules_1.derivePhysicalPinCount)(payload.pinout),
		        arrayConfirmed: true,
		        pinAssignments,
		        ncPins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
		        ...noConnectFloatingBehaviorField((0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(payload)),
		        parameters: (0, array_resolution_adapter_1.readArrayElementParametersFromSubModels)(payload.spiceModels),
		    };
		}
		function arrayFormStateToCanonical(formState, resolver) {
		    const elementType = formState.elementSigmaModelType ?? formState.parentSigmaModelType;
		    const config = (0, array_submodels_1.getArrayElementConfig)(elementType);
		    const prefix = config?.prefix ?? '';
		    const elementDefinition = resolver(elementType);
		    const terminalById = new Map((elementDefinition?.signals ?? []).map((signal) => [String(signal.id), signal]));
		    const added = flagOperatorAddedPinAssignments(formState.pinAssignments);
		    const signals = [];
		    const compositeSignals = [];
		    const pinout = {};
		    let maxIndex = -1;
		    for (const [index, assignment] of formState.pinAssignments.entries()) {
		        if (assignment.block === null) {
		            continue;
		        }
		        const pins = (0, pin_count_rules_1.normalizePinDesignators)(assignment.pins);
		        if (isUnboundPinAssignment(assignment)) {
		            if (pins.length === 0) {
		                continue;
		            }
		            const signal = unboundCanonicalSignal(assignment);
		            signals.push(signal);
		            pinout[String(signal.id)] = pins;
		            continue;
		        }
		        maxIndex = Math.max(maxIndex, assignment.block.index);
		        const terminal = terminalById.get(assignment.modelSignalId);
		        if (isUnpinnedOptional(terminal, pins) || (added[index] === true && pins.length === 0)) {
		            continue;
		        }
		        const submodelId = `${prefix}${assignment.block.index + 1}`;
		        const terminalName = terminal?.name ?? assignment.modelSignalId;
		        const structuralName = (0, array_submodels_1.composeArraySignalName)(submodelId, terminalName);
		        const name = assignment.nameOverride ?? structuralName;
		        const id = assignment.packageSignalId ?? freshSignalId(structuralName);
		        signals.push(buildArrayCompositeSignal({
		            terminal,
		            submodelId,
		            terminalName,
		            name,
		            id,
		            floatingBehavior: assignment.floatingBehavior,
		        }));
		        compositeSignals.push({ id, name: structuralName });
		        pinout[id] = pins;
		    }
		    const spiceModels = elementDefinition !== null ?
		        (0, array_resolution_adapter_1.buildArrayFormSpiceModels)({
		            arrayForm: {
		                confirmed: true,
		                elementModelType: elementType,
		                elementCount: maxIndex + 1,
		                submodelPrefix: prefix,
		            },
		            signals: compositeSignals,
		            parameters: formState.parameters,
		            parameterDefs: elementDefinition.parameters,
		            elementSignals: elementDefinition.signals.map((signal) => ({
		                id: signal.id,
		                name: signal.name,
		                isOptional: signal.isOptional,
		            })),
		        })
		        : [];
		    appendNoConnectSignal(signals, pinout, formState.ncPins, formState.ncFloatingBehavior);
		    return {
		        sigmaModelType: config?.parentType ?? formState.parentSigmaModelType,
		        parameters: {},
		        signals,
		        pinout,
		        spiceModels,
		    };
		}
		function modelSignalIdOf(signal) {
		    return String(signal.modelSignal ?? signal.id);
		}
		function hasModelSignalRef(signal) {
		    return signal.modelSignal !== undefined && signal.modelSignal !== null;
		}
		function freshSignalId(name) {
		    const id = (0, generate_readable_id_1.generateReadableId)(CadstromUid_1.CadstromObjectType.Signal, name, 0);
		    return id === no_connect_signal_1.NO_CONNECT_SIGNAL_ID ? `${id}_signal` : id;
		}
		function freshUnboundSignalId(name) {
		    return `${(0, generate_readable_id_1.generateReadableId)(CadstromUid_1.CadstromObjectType.Signal, name, 0)}__added`;
		}
		function unboundAssignment(signal, block, pinout) {
		    return withFloatingBehaviorChoice({
		        modelSignalId: exports.UNBOUND_MODEL_SIGNAL_ID,
		        block,
		        nameOverride: signal.name,
		        pins: [...(pinout[String(signal.id)] ?? [])],
		        packageSignalId: String(signal.id),
		    }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(undefined, signal.floatingBehavior));
		}
		function unboundCanonicalSignal(assignment) {
		    const name = assignment.nameOverride ?? '';
		    return (0, form_model_shape_1.buildUnboundCanonicalSignal)({
		        id: assignment.packageSignalId ?? freshUnboundSignalId(name),
		        name,
		        floatingBehavior: assignment.floatingBehavior,
		        blockInfo: assignment.block !== null ? { name: assignment.block.name, index: assignment.block.index } : undefined,
		    });
		}
		function fallbackSignal(modelSignalId, name, floatingBehavior) {
		    return {
		        id: modelSignalId,
		        name,
		        padRefs: [],
		        pinRefs: [],
		        modelSignal: modelSignalId,
		        ...(0, form_model_shape_1.floatingBehaviorField)(floatingBehavior),
		    };
		}
		function withFloatingBehaviorChoice(assignment, floatingBehavior) {
		    return floatingBehavior !== undefined ? { ...assignment, floatingBehavior } : assignment;
		}
		function noConnectFloatingBehaviorField(ncFloatingBehavior) {
		    return ncFloatingBehavior !== undefined ? { ncFloatingBehavior } : {};
		}
		function appendNoConnectSignal(signals, pinout, ncPins, ncFloatingBehavior) {
		    const pins = (0, pin_count_rules_1.normalizePinDesignators)(ncPins);
		    if (pins.length === 0) {
		        return;
		    }
		    const ncSignal = (0, no_connect_signal_1.buildNoConnectSignal)(ncFloatingBehavior);
		    signals.push(ncSignal);
		    pinout[String(ncSignal.id)] = pins;
		}
		function isUnpinnedOptional(modelSignal, pins) {
		    return modelSignal !== undefined && modelSignal.isOptional && pins.length === 0;
		}
		function blockRefFor(name, index) {
		    if (name === null || name === '') {
		        return null;
		    }
		    return { name, index: name === 'pwr' ? 0 : Math.max(index, 0) };
		}
		function blockRefOf(signal, modelSignal) {
		    return blockRefFor(signal.blockInfo?.name ?? modelSignal?.repeatableBlock ?? null, signal.blockInfo?.index ?? 0);
		}
		function compareRepeatableBlocks(left, right) {
		    const leftIsPwr = left?.name === 'pwr';
		    const rightIsPwr = right?.name === 'pwr';
		    if (leftIsPwr !== rightIsPwr) {
		        return leftIsPwr ? -1 : 1;
		    }
		    const nameCompare = (left?.name ?? '').localeCompare(right?.name ?? '', undefined, {
		        numeric: true,
		        sensitivity: 'base',
		    });
		    return nameCompare !== 0 ? nameCompare : (left?.index ?? 0) - (right?.index ?? 0);
		}
		function parseSubModelIndex(subModelId) {
		    const ref = (0, array_submodels_1.parseSubModelId)(subModelId);
		    return ref !== null ? ref.ordinal - 1 : 0;
		}
		function buildArrayCompositeSignal(args) {
		    const { terminal, submodelId, terminalName, name, id, floatingBehavior } = args;
		    return {
		        id,
		        name,
		        padRefs: [],
		        pinRefs: [],
		        signalDirection: terminal?.direction ?? undefined,
		        attributes: { ...(terminal?.attributes ?? {}) },
		        description: `${submodelId} - ${terminalName}`,
		        ...(0, form_model_shape_1.floatingBehaviorField)((0, form_model_shape_1.resolveFormFloatingBehavior)(terminal, floatingBehavior)),
		        supportedDriveModes: [],
		        ties: [],
		        isCadstromInternal: false,
		    };
		}
		function buildInitialFormState(sigmaModelType, resolver, seed = null) {
		    const definition = resolver(sigmaModelType);
		    const matchesSeed = seed !== null && seed.sigmaModelType !== '' && sigmaModelType === seed.sigmaModelType;
		    const classInferredPinCount = (0, pin_count_rules_1.inferPinCountFromDefinition)(definition);
		    if (matchesSeed && seed !== null && definition !== null && definition.repeatableBlocks.length > 0) {
		        return payloadToRepeatableBlockFormState(seed, definition);
		    }
		    const seedAdded = matchesSeed && seed !== null && definition !== null ? seedOperatorAddedAssignments(seed, definition) : [];
		    const pinoutForClass = definition === null ? {} : resolveInitialPinout(definition, classInferredPinCount, seed, matchesSeed, seedAdded);
		    const seedSignalByModelSignalId = matchesSeed && seed !== null ? buildSeedSignalByModelSignalId(seed) : new Map();
		    const ncPins = matchesSeed && seed !== null ? (0, no_connect_signal_1.readNoConnectPinsFromPayload)(seed) : [];
		    const pinAssignments = definition === null || definition.repeatableBlocks.length > 0 ?
		        []
		        : [
		            ...definition.signals.map((modelSignal) => {
		                const modelSignalId = String(modelSignal.id);
		                const seedSignal = seedSignalByModelSignalId.get(modelSignalId);
		                const nameOverride = seedSignal !== undefined && seedSignal.name !== modelSignal.name ? seedSignal.name : null;
		                return withFloatingBehaviorChoice({ modelSignalId, block: null, nameOverride, pins: [...(pinoutForClass[modelSignalId] ?? [])] }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, seedSignal?.floatingBehavior));
		            }),
		            ...seedAdded,
		        ];
		    return {
		        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
		        parentSigmaModelType: sigmaModelType,
		        elementSigmaModelType: null,
		        pinCount: (0, pin_count_rules_1.resolveSeedPinCount)(definition, pinoutForClass, [
		            ...ncPins,
		            ...seedAdded.flatMap((assignment) => assignment.pins),
		        ]),
		        arrayConfirmed: null,
		        pinAssignments,
		        ncPins,
		        ...noConnectFloatingBehaviorField(matchesSeed && seed !== null ? (0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(seed) : undefined),
		        parameters: matchesSeed && seed !== null ? seed.parameters : {},
		    };
		}
		function remapSeedPinoutForModelSignals(seedPinout, seedSignals, modelSignals) {
		    const pinout = {};
		    const modelSignalIds = new Set(modelSignals.map((modelSignal) => String(modelSignal.id)));
		    const appendPins = (modelSignalId, pins) => {
		        if (!modelSignalIds.has(modelSignalId)) {
		            return;
		        }
		        const nonEmpty = (0, pin_count_rules_1.normalizePinDesignators)(pins);
		        if (nonEmpty.length === 0) {
		            return;
		        }
		        pinout[modelSignalId] = [...new Set([...(pinout[modelSignalId] ?? []), ...nonEmpty])];
		    };
		    const seedSignalIds = new Set(seedSignals.map((seedSignal) => String(seedSignal.id)));
		    for (const seedSignal of seedSignals) {
		        const { modelSignal } = seedSignal;
		        const hasExplicitTarget = (typeof modelSignal === 'string' && modelSignal !== '') || typeof modelSignal === 'number';
		        const target = hasExplicitTarget ? String(modelSignal) : String(seedSignal.id);
		        appendPins(target, seedPinout[String(seedSignal.id)] ?? []);
		    }
		    for (const [seedSignalId, pins] of Object.entries(seedPinout)) {
		        if (seedSignalIds.has(seedSignalId)) {
		            continue;
		        }
		        appendPins(seedSignalId, pins);
		    }
		    return pinout;
		}
		function modelHasImplicitTwoPinLayout(modelSignals) {
		    return modelSignals.length === 2 && modelSignals.every((signal) => !signal.isOptional);
		}
		function buildImplicitTwoPinPinout(modelSignals) {
		    const pinout = {};
		    const [first, second] = modelSignals;
		    if (first !== undefined) {
		        pinout[String(first.id)] = ['1'];
		    }
		    if (second !== undefined) {
		        pinout[String(second.id)] = ['2'];
		    }
		    return pinout;
		}
		function resolveInitialPinout(definition, classInferredPinCount, seed, matchesSeed, seedAdded) {
		    const seedPhysicalPinCount = matchesSeed && seed !== null ? (0, pin_count_rules_1.derivePhysicalPinCount)(seed.pinout) : null;
		    if (classInferredPinCount === 2 &&
		        modelHasImplicitTwoPinLayout(definition.signals) &&
		        (seedPhysicalPinCount === null || seedPhysicalPinCount === 2)) {
		        return buildImplicitTwoPinPinout(definition.signals);
		    }
		    if (matchesSeed && seed !== null) {
		        const seedTerminals = withoutSeedSignals(seed, seedAdded);
		        const remapped = remapSeedPinoutForModelSignals(seedTerminals.pinout, seedTerminals.signals, definition.signals);
		        if (classInferredPinCount === null || (0, pin_count_rules_1.derivePhysicalPinCount)(remapped) === classInferredPinCount) {
		            return remapped;
		        }
		    }
		    return {};
		}
		function seedOperatorAddedAssignments(seed, definition) {
		    const seeded = seedSimpleAssignments(seed, definition);
		    const added = flagOperatorAddedPinAssignments(seeded);
		    return seeded.filter((_, index) => added[index] === true);
		}
		function withoutSeedSignals(seed, assignments) {
		    const dropped = new Set(assignments.flatMap((assignment) => assignment.packageSignalId ?? []));
		    if (dropped.size === 0) {
		        return seed;
		    }
		    return {
		        ...seed,
		        signals: seed.signals.filter((signal) => !dropped.has(String(signal.id))),
		        pinout: Object.fromEntries(Object.entries(seed.pinout).filter(([signalId]) => !dropped.has(signalId))),
		    };
		}
		function buildSeedSignalByModelSignalId(seed) {
		    const seedSignalByModelSignalId = new Map();
		    for (const signal of seed.signals) {
		        if (signal.modelSignal === undefined) {
		            continue;
		        }
		        const key = String(signal.modelSignal);
		        if (!seedSignalByModelSignalId.has(key)) {
		            seedSignalByModelSignalId.set(key, signal);
		        }
		    }
		    return seedSignalByModelSignalId;
		}
		exports.SIGMA_MODEL_ENTRY_FORM_STATE_CODEC = new SigmaModelEntryFormStateCodec();
		exports.sigmaModelEntryFormLatest = exports.SIGMA_MODEL_ENTRY_FORM_STATE_CODEC;
		
	} (formLatest$2));
	return formLatest$2;
}

var generalPackageSlices = {};

var fastDeepEqual;
var hasRequiredFastDeepEqual;

function requireFastDeepEqual () {
	if (hasRequiredFastDeepEqual) return fastDeepEqual;
	hasRequiredFastDeepEqual = 1;

	// do not edit .js files directly - edit src/index.jst



	fastDeepEqual = function equal(a, b) {
	  if (a === b) return true;

	  if (a && b && typeof a == 'object' && typeof b == 'object') {
	    if (a.constructor !== b.constructor) return false;

	    var length, i, keys;
	    if (Array.isArray(a)) {
	      length = a.length;
	      if (length != b.length) return false;
	      for (i = length; i-- !== 0;)
	        if (!equal(a[i], b[i])) return false;
	      return true;
	    }



	    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
	    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
	    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

	    keys = Object.keys(a);
	    length = keys.length;
	    if (length !== Object.keys(b).length) return false;

	    for (i = length; i-- !== 0;)
	      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

	    for (i = length; i-- !== 0;) {
	      var key = keys[i];

	      if (!equal(a[key], b[key])) return false;
	    }

	    return true;
	  }

	  // true if both NaN, false otherwise
	  return a!==a && b!==b;
	};
	return fastDeepEqual;
}

var boundaryConditionsDenormalized = {};

var hasRequiredBoundaryConditionsDenormalized;

function requireBoundaryConditionsDenormalized () {
	if (hasRequiredBoundaryConditionsDenormalized) return boundaryConditionsDenormalized;
	hasRequiredBoundaryConditionsDenormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OcsValidationCheckType = exports.AmrValidationCheckType = exports.AbsoluteMaximumRatingsDenormalizedSchema = exports.OperatingCharacteristicsDenormalizedSchema = exports.BoundaryConditionDenormalizedSchema = void 0;
		exports.validateAndCleanDenormalizedAMRs = validateAndCleanDenormalizedAMRs;
		exports.validateAndCleanDenormalizedOCs = validateAndCleanDenormalizedOCs;
		exports.mapBoundarySignalNamesToSignalIds = mapBoundarySignalNamesToSignalIds;
		exports.convertHilAmrToDenormalized = convertHilAmrToDenormalized;
		exports.convertHilOcToDenormalized = convertHilOcToDenormalized;
		exports.convertBoundariesToAmrDenormalized = convertBoundariesToAmrDenormalized;
		exports.convertBoundariesToOcDenormalized = convertBoundariesToOcDenormalized;
		exports.remapSignalIdsInBoundaries = remapSignalIdsInBoundaries;
		const z = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		exports.BoundaryConditionDenormalizedSchema = sigma_engine_shared_1.HilOperatingConditionBoundarySchema.omit({
		    signalId: true,
		    minimumRelativeToSignalId: true,
		    nominalRelativeToSignalId: true,
		    maximumRelativeToSignalId: true,
		})
		    .extend({
		    signalName: z.string().describe('The signal name that the operating characteristic applies to.'),
		    minimum: z
		        .string()
		        .nullable()
		        .describe('The minimum value of the characteristic in the appropriate SI units for the characteristic.'),
		    minimumRelativeToSignalName: z
		        .string()
		        .nullable()
		        .describe('The name of the signal that the minimum value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
		    nominal: z
		        .string()
		        .nullable()
		        .describe('The nominal value of the characteristic in the appropriate SI units for the characteristic.'),
		    nominalRelativeToSignalName: z
		        .string()
		        .nullable()
		        .describe('The name of the signal that the nominal value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
		    maximum: z
		        .string()
		        .nullable()
		        .describe('The maximum value of the characteristic in the appropriate SI units for the characteristic.'),
		    maximumRelativeToSignalName: z
		        .string()
		        .nullable()
		        .describe('The name of the signal that the maximum value is relative to, if the characteristic requires a relative signal. Characteristics like Voltage and Power require a relative signal, while others like Current and Temperature do not.'),
		})
		    .strict()
		    .describe(sigma_engine_shared_1.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
		exports.OperatingCharacteristicsDenormalizedSchema = exports.BoundaryConditionDenormalizedSchema;
		exports.AbsoluteMaximumRatingsDenormalizedSchema = exports.BoundaryConditionDenormalizedSchema.omit({
		    nominal: true,
		    nominalRelativeToSignalName: true,
		}).describe(sigma_engine_shared_1.OPERATING_CONDITION_BOUNDARY_DESCRIPTION);
		var AmrValidationCheckType;
		(function (AmrValidationCheckType) {
		    AmrValidationCheckType["ValuesShouldBeNumericSIParseable"] = "ValuesShouldBeNumericSIParseable";
		    AmrValidationCheckType["RelativeSignalShouldBeValidSignal"] = "RelativeSignalShouldBeValidSignal";
		    AmrValidationCheckType["RelativeSignalShouldNotBeMissing"] = "RelativeSignalShouldNotBeMissing";
		    AmrValidationCheckType["RatingShouldHaveAtLeastOneTargetSignal"] = "RatingShouldHaveAtLeastOneTargetSignal";
		    AmrValidationCheckType["TargetAndRelativeSignalShouldNotBeEqual"] = "TargetAndRelativeSignalShouldNotBeEqual";
		    AmrValidationCheckType["RatingsShouldNotBeDuplicated"] = "RatingsShouldNotBeDuplicated";
		    AmrValidationCheckType["MinimumAndMaximumShouldDiffer"] = "MinimumAndMaximumShouldDiffer";
		    AmrValidationCheckType["AllNonGroundNonNoConnectSignalsShouldHaveAmr"] = "AllNonGroundNonNoConnectSignalsShouldHaveAmr";
		    AmrValidationCheckType["ParentRowLineageShouldMatchRawRow"] = "ParentRowLineageShouldMatchRawRow";
		})(AmrValidationCheckType || (exports.AmrValidationCheckType = AmrValidationCheckType = {}));
		var OcsValidationCheckType;
		(function (OcsValidationCheckType) {
		    OcsValidationCheckType["ValuesShouldBeNumericSIParseable"] = "ValuesShouldBeNumericSIParseable";
		    OcsValidationCheckType["RelativeSignalShouldBeValidSignal"] = "RelativeSignalShouldBeValidSignal";
		    OcsValidationCheckType["RelativeSignalShouldNotBeMissing"] = "RelativeSignalShouldNotBeMissing";
		    OcsValidationCheckType["ConditionShouldHaveAtLeastOneTargetSignal"] = "ConditionShouldHaveAtLeastOneTargetSignal";
		    OcsValidationCheckType["GroundSignalsShouldNotBeAssignedConditions"] = "GroundSignalsShouldNotBeAssignedConditions";
		    OcsValidationCheckType["TargetAndRelativeSignalShouldNotBeEqual"] = "TargetAndRelativeSignalShouldNotBeEqual";
		    OcsValidationCheckType["ConditionsShouldNotBeDuplicated"] = "ConditionsShouldNotBeDuplicated";
		    OcsValidationCheckType["MinimumNominalAndMaximumShouldDiffer"] = "MinimumNominalAndMaximumShouldDiffer";
		    OcsValidationCheckType["ParentRowLineageShouldMatchRawRow"] = "ParentRowLineageShouldMatchRawRow";
		})(OcsValidationCheckType || (exports.OcsValidationCheckType = OcsValidationCheckType = {}));
		function validateAndCleanDenormalizedAMRs(amrs, signals, logger, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
		    let validatedResults = amrs;
		    const allValidationResults = [];
		    const { validatedEntries: afterMinSICheck, validationResults: minSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'minimum', characteristic, {
		        ignoreEmpty: true,
		    }), AmrValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
		    validatedResults = afterMinSICheck;
		    allValidationResults.push(...minSIResults);
		    const { validatedEntries: afterMaxSICheck, validationResults: maxSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'maximum', characteristic, {
		        ignoreEmpty: true,
		    }), AmrValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
		    validatedResults = afterMaxSICheck;
		    allValidationResults.push(...maxSIResults);
		    const signalNames = signals.map((s) => s.signalName);
		    const signalNamesSet = new Set(signalNames);
		    const { validatedEntries: afterMinRelativeCheck, validationResults: minRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'minimumRelativeToSignalName', signalNamesSet, {
		        ignoreEmpty: true,
		    }), AmrValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
		    validatedResults = afterMinRelativeCheck;
		    allValidationResults.push(...minRelativeResults);
		    const { validatedEntries: afterMaxRelativeCheck, validationResults: maxRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'maximumRelativeToSignalName', signalNamesSet, {
		        ignoreEmpty: true,
		    }), AmrValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
		    validatedResults = afterMaxRelativeCheck;
		    allValidationResults.push(...maxRelativeResults);
		    const { validatedEntries: afterMinTargetCheck, validationResults: minTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'minimumRelativeToSignalName'), AmrValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
		    validatedResults = afterMinTargetCheck;
		    allValidationResults.push(...minTargetResults);
		    const { validatedEntries: afterMaxTargetCheck, validationResults: maxTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'maximumRelativeToSignalName'), AmrValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
		    validatedResults = afterMaxTargetCheck;
		    allValidationResults.push(...maxTargetResults);
		    const { validatedEntries: afterMinMaxDifferCheck, validationResults: minMaxDifferResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => checkMinimumAndMaximumDiffer(entries, characteristic), AmrValidationCheckType.MinimumAndMaximumShouldDiffer, { dropFailingEntries: true, logger });
		    validatedResults = afterMinMaxDifferCheck;
		    allValidationResults.push(...minMaxDifferResults);
		    const { validatedEntries: afterDuplicateCheck, validationResults: duplicateResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkForDuplicateEntries(entries), AmrValidationCheckType.RatingsShouldNotBeDuplicated, { dropFailingEntries: true, logger });
		    validatedResults = afterDuplicateCheck;
		    allValidationResults.push(...duplicateResults);
		    const missingSignalsResult = checkAllSignalsHaveAmr(validatedResults, signals);
		    allValidationResults.push(missingSignalsResult);
		    return {
		        validatedResults,
		        validationResults: (0, sigma_engine_shared_1.compactValidationResults)(allValidationResults),
		    };
		}
		function checkMinimumAndMaximumDiffer(entries, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
		    const failures = [];
		    const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
		    entries.forEach((entry, i) => {
		        if (entry.minimum === null || entry.maximum === null) {
		            return;
		        }
		        try {
		            const minValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.minimum, siDefinition);
		            const maxValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.maximum, siDefinition);
		            if (minValue !== undefined && maxValue !== undefined && Math.abs(minValue - maxValue) < siDefinition.epsilon) {
		                failures.push({
		                    index: i,
		                    message: `Minimum and maximum values are equal: ${entry.minimum} === ${entry.maximum}`,
		                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
		                });
		            }
		        }
		        catch (error) {
		            return;
		        }
		    });
		    return failures;
		}
		function checkAllSignalsHaveAmr(amrs, signals) {
		    const amrSignalNames = new Set(amrs.map((a) => a.signalName));
		    const requiredSignals = signals.filter((s) => !s.ground && !s.noConnect);
		    const missingSignals = requiredSignals.filter((s) => !amrSignalNames.has(s.signalName));
		    if (missingSignals.length === 0) {
		        return {
		            type: AmrValidationCheckType.AllNonGroundNonNoConnectSignalsShouldHaveAmr,
		            message: 'All required signals have AMR entries',
		            result: sigma_engine_shared_1.ValidationCheckResultStatus.pass,
		        };
		    }
		    const missingNames = missingSignals.map((s) => s.signalName).join(', ');
		    return {
		        type: AmrValidationCheckType.AllNonGroundNonNoConnectSignalsShouldHaveAmr,
		        message: `Missing AMR entries for ${missingSignals.length} signals: ${missingNames}`,
		        result: sigma_engine_shared_1.ValidationCheckResultStatus.fail,
		        failures: missingSignals.map((s, i) => ({
		            index: i,
		            message: `Signal '${s.signalName}' is missing an AMR entry`,
		            details: `Category: power=${s.power}, analog=${s.analog}, digital=${s.digital}`,
		        })),
		    };
		}
		function validateAndCleanDenormalizedOCs(ocs, signals, logger, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
		    let validatedResults = ocs;
		    const allValidationResults = [];
		    const { validatedEntries: afterMinSICheck, validationResults: minSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'minimum', characteristic, {
		        ignoreEmpty: true,
		    }), OcsValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
		    validatedResults = afterMinSICheck;
		    allValidationResults.push(...minSIResults);
		    const { validatedEntries: afterNominalSICheck, validationResults: nominalSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'nominal', characteristic, {
		        ignoreEmpty: true,
		    }), OcsValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
		    validatedResults = afterNominalSICheck;
		    allValidationResults.push(...nominalSIResults);
		    const { validatedEntries: afterMaxSICheck, validationResults: maxSIResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueSIParseability(entries, 'maximum', characteristic, {
		        ignoreEmpty: true,
		    }), OcsValidationCheckType.ValuesShouldBeNumericSIParseable, { dropFailingEntries: true, logger });
		    validatedResults = afterMaxSICheck;
		    allValidationResults.push(...maxSIResults);
		    const signalNames = signals.map((s) => s.signalName);
		    const signalNamesSet = new Set(signalNames);
		    const { validatedEntries: afterMinRelativeCheck, validationResults: minRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'minimumRelativeToSignalName', signalNamesSet, {
		        ignoreEmpty: true,
		    }), OcsValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
		    validatedResults = afterMinRelativeCheck;
		    allValidationResults.push(...minRelativeResults);
		    const { validatedEntries: afterNominalRelativeCheck, validationResults: nominalRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'nominalRelativeToSignalName', signalNamesSet, {
		        ignoreEmpty: true,
		    }), OcsValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
		    validatedResults = afterNominalRelativeCheck;
		    allValidationResults.push(...nominalRelativeResults);
		    const { validatedEntries: afterMaxRelativeCheck, validationResults: maxRelativeResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkValueBelongsToSet(entries, 'maximumRelativeToSignalName', signalNamesSet, {
		        ignoreEmpty: true,
		    }), OcsValidationCheckType.RelativeSignalShouldBeValidSignal, { dropFailingEntries: true, logger });
		    validatedResults = afterMaxRelativeCheck;
		    allValidationResults.push(...maxRelativeResults);
		    const { validatedEntries: afterMinTargetCheck, validationResults: minTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'minimumRelativeToSignalName'), OcsValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
		    validatedResults = afterMinTargetCheck;
		    allValidationResults.push(...minTargetResults);
		    const { validatedEntries: afterNominalTargetCheck, validationResults: nominalTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'nominalRelativeToSignalName'), OcsValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
		    validatedResults = afterNominalTargetCheck;
		    allValidationResults.push(...nominalTargetResults);
		    const { validatedEntries: afterMaxTargetCheck, validationResults: maxTargetResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkPropertyValuesDiffer(entries, 'signalName', 'maximumRelativeToSignalName'), OcsValidationCheckType.TargetAndRelativeSignalShouldNotBeEqual, { dropFailingEntries: true, logger });
		    validatedResults = afterMaxTargetCheck;
		    allValidationResults.push(...maxTargetResults);
		    const { validatedEntries: afterMinNomMaxDifferCheck, validationResults: minNomMaxDifferResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => checkMinimumNominalAndMaximumDiffer(entries, characteristic), OcsValidationCheckType.MinimumNominalAndMaximumShouldDiffer, { dropFailingEntries: true, logger });
		    validatedResults = afterMinNomMaxDifferCheck;
		    allValidationResults.push(...minNomMaxDifferResults);
		    const { validatedEntries: afterDuplicateCheck, validationResults: duplicateResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(validatedResults, (entries) => sigma_engine_shared_1.TableValidationTools.checkForDuplicateEntries(entries), OcsValidationCheckType.ConditionsShouldNotBeDuplicated, { dropFailingEntries: true, logger });
		    validatedResults = afterDuplicateCheck;
		    allValidationResults.push(...duplicateResults);
		    return {
		        validatedResults,
		        validationResults: (0, sigma_engine_shared_1.compactValidationResults)(allValidationResults),
		    };
		}
		function checkMinimumNominalAndMaximumDiffer(entries, characteristic = sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
		    const failures = [];
		    const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
		    entries.forEach((entry, i) => {
		        try {
		            const minValue = entry.minimum ? sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.minimum, siDefinition) : undefined;
		            const nominalValue = entry.nominal ? sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.nominal, siDefinition) : undefined;
		            const maxValue = entry.maximum ? sigma_engine_shared_1.Utils.parseNumericalSIValue(entry.maximum, siDefinition) : undefined;
		            if (minValue !== undefined && maxValue !== undefined && Math.abs(minValue - maxValue) < siDefinition.epsilon) {
		                failures.push({
		                    index: i,
		                    message: `Minimum and maximum values are equal: ${entry.minimum} === ${entry.maximum}`,
		                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
		                });
		            }
		            if (minValue !== undefined &&
		                nominalValue !== undefined &&
		                Math.abs(minValue - nominalValue) < siDefinition.epsilon) {
		                failures.push({
		                    index: i,
		                    message: `Minimum and nominal values are equal: ${entry.minimum} === ${entry.nominal}`,
		                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
		                });
		            }
		            if (nominalValue !== undefined &&
		                maxValue !== undefined &&
		                Math.abs(nominalValue - maxValue) < siDefinition.epsilon) {
		                failures.push({
		                    index: i,
		                    message: `Nominal and maximum values are equal: ${entry.nominal} === ${entry.maximum}`,
		                    details: `Signal: ${entry.signalName}, Characteristic: ${entry.characteristic}`,
		                });
		            }
		        }
		        catch (error) {
		            return;
		        }
		    });
		    return failures;
		}
		function mapBoundarySignalNamesToSignalIds(boundaries, signals, type, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
		    const schema = type === 'amr' ? sigma_engine_shared_1.HilAbsoluteMaximumRatingBoundarySchema : sigma_engine_shared_1.HilOperatingConditionBoundarySchema;
		    return boundaries
		        .map((b) => {
		        const signalId = (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.signalName, signals);
		        if (!signalId) {
		            logger.warn('MapBoundarySignalNamesToSignalIds_SignalNotFound', {
		                signalName: b.signalName,
		                boundary: b,
		            });
		            return null;
		        }
		        const boundary = {
		            signalId: signalId,
		            characteristic: b.characteristic,
		            minimum: b.minimum,
		            minimumRelativeToSignalId: b.minimumRelativeToSignalName ?
		                (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.minimumRelativeToSignalName, signals, logger)
		                : null,
		            nominal: b.nominal ?? null,
		            nominalRelativeToSignalId: b.nominalRelativeToSignalName ?
		                (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.nominalRelativeToSignalName, signals, logger)
		                : null,
		            maximum: b.maximum,
		            maximumRelativeToSignalId: b.maximumRelativeToSignalName ?
		                (0, sigma_engine_shared_1.findSignalIdBySignalName)(b.maximumRelativeToSignalName, signals, logger)
		                : null,
		        };
		        const parseResult = schema.safeParse(boundary);
		        if (!parseResult.success) {
		            logger.warn('MapBoundarySignalNamesToSignalIds_ValidationFailed', {
		                signalName: b.signalName,
		                errors: parseResult.error.flatten().fieldErrors,
		                boundary: b,
		            });
		            return null;
		        }
		        return parseResult.data;
		    })
		        .filter((boundary) => boundary !== null);
		}
		function convertHilAmrToDenormalized(hilAmrs, signals) {
		    const result = [];
		    for (const amr of hilAmrs) {
		        const targetSignal = signals.find((s) => s.id === amr.signalId);
		        if (!targetSignal) {
		            continue;
		        }
		        const minRelSignal = amr.minimumRelativeToSignalId ? signals.find((s) => s.id === amr.minimumRelativeToSignalId) : null;
		        const maxRelSignal = amr.maximumRelativeToSignalId ? signals.find((s) => s.id === amr.maximumRelativeToSignalId) : null;
		        result.push({
		            signalName: targetSignal.name,
		            characteristic: amr.characteristic,
		            minimum: amr.minimum ?? null,
		            minimumRelativeToSignalName: minRelSignal?.name ?? null,
		            maximum: amr.maximum ?? null,
		            maximumRelativeToSignalName: maxRelSignal?.name ?? null,
		        });
		    }
		    return result;
		}
		function convertHilOcToDenormalized(hilOcs, signals) {
		    const result = [];
		    for (const oc of hilOcs) {
		        const targetSignal = signals.find((s) => s.id === oc.signalId);
		        if (!targetSignal) {
		            continue;
		        }
		        const minRelSignal = oc.minimumRelativeToSignalId ? signals.find((s) => s.id === oc.minimumRelativeToSignalId) : null;
		        const nomRelSignal = oc.nominalRelativeToSignalId ? signals.find((s) => s.id === oc.nominalRelativeToSignalId) : null;
		        const maxRelSignal = oc.maximumRelativeToSignalId ? signals.find((s) => s.id === oc.maximumRelativeToSignalId) : null;
		        result.push({
		            signalName: targetSignal.name,
		            characteristic: oc.characteristic,
		            minimum: oc.minimum ?? null,
		            minimumRelativeToSignalName: minRelSignal?.name ?? null,
		            nominal: oc.nominal ?? null,
		            nominalRelativeToSignalName: nomRelSignal?.name ?? null,
		            maximum: oc.maximum ?? null,
		            maximumRelativeToSignalName: maxRelSignal?.name ?? null,
		        });
		    }
		    return result;
		}
		function convertBoundariesToAmrDenormalized(boundaries, signals) {
		    const hilAmrs = sigma_engine_shared_1.BoundaryMapperUtil.getAmrBoundaries(signals, boundaries);
		    return convertHilAmrToDenormalized(hilAmrs, signals);
		}
		function convertBoundariesToOcDenormalized(boundaries, signals) {
		    const hilOcs = sigma_engine_shared_1.BoundaryMapperUtil.getOperatingConditionBoundaries(signals, boundaries);
		    return convertHilOcToDenormalized(hilOcs, signals);
		}
		const signal_id_remap_internal_1 = requireSignalIdRemapInternal();
		function remapSignalIdsInBoundaries(boundaries, signalIdMap, opts) {
		    if (boundaries.length === 0) {
		        return [];
		    }
		    const cloned = JSON.parse(JSON.stringify(boundaries));
		    cloned.forEach((b, i) => {
		        const path = `boundaries[${i}].boundary`;
		        (0, signal_id_remap_internal_1.remapBoundaryDefinition)(b.boundary, signalIdMap, path, opts);
		        const conditions = b.conditions;
		        if (Array.isArray(conditions)) {
		            conditions.forEach((c, ci) => {
		                const cond = c;
		                if (cond.boundary) {
		                    (0, signal_id_remap_internal_1.remapBoundaryDefinition)(cond.boundary, signalIdMap, `boundaries[${i}].conditions[${ci}].boundary`, opts);
		                }
		            });
		        }
		    });
		    return cloned;
		}
		
	} (boundaryConditionsDenormalized));
	return boundaryConditionsDenormalized;
}

var conditionModeDenormalized = {};

var hasRequiredConditionModeDenormalized;

function requireConditionModeDenormalized () {
	if (hasRequiredConditionModeDenormalized) return conditionModeDenormalized;
	hasRequiredConditionModeDenormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DriveModeVoltageConditionValidationCheckType = exports.ConditionModeDenormalizedSchema = void 0;
		exports.convertConditionModeSignalsFromIdsToNames = convertConditionModeSignalsFromIdsToNames;
		exports.convertConditionModeSignalsFromNamesToIds = convertConditionModeSignalsFromNamesToIds;
		exports.getVoltageConditionModeDenormalizedSchemaWithInjectedSignalIds = getVoltageConditionModeDenormalizedSchemaWithInjectedSignalIds;
		exports.convertConditionModeToModeBase = convertConditionModeToModeBase;
		exports.convertModeBaseToConditionModeDenormalized = convertModeBaseToConditionModeDenormalized;
		exports.remapSignalIdsInModes = remapSignalIdsInModes;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const sigma_engine_shared_2 = requireDist$1();
		exports.ConditionModeDenormalizedSchema = zod_1.z.object({
		    id: zod_1.z.string().describe('A unique id for this mode. Alphanumeric only.'),
		    characteristic: zod_1.z.nativeEnum(sigma_engine_shared_2.BoundaryCharacteristic).describe('The characteristic being measured.'),
		    minVal: zod_1.z
		        .number()
		        .nullable()
		        .describe('The minimum characteristic value of the mode in base units. If not provided, there is no minimum. One or both of minVal and maxVal must be provided.'),
		    nominalVal: zod_1.z
		        .number()
		        .nullable()
		        .describe('The nominal (absolute) value of this condition, if stated as a single operating point, otherwise set to null.'),
		    maxVal: zod_1.z
		        .number()
		        .nullable()
		        .describe('The maximum charactistic value of the mode in base volts. If not provided, there is no maximum. One or both of minVal and maxVal must be provided.'),
		    targetSignalId: zod_1.z.string().describe('The signal ID this mode applies to'),
		    relativeSignalId: zod_1.z.string().describe('The signal ID that minVal and maxVal should be measured relative to'),
		});
		function convertConditionModeSignalsFromIdsToNames(modes, signals, logger) {
		    return modes
		        .map((mode) => {
		        const targetSignalName = (0, sigma_engine_shared_1.findSignalNameBySignalId)(mode.targetSignalId, signals, logger);
		        const relativeSignalName = (0, sigma_engine_shared_1.findSignalNameBySignalId)(mode.relativeSignalId, signals, logger);
		        if (!targetSignalName || !relativeSignalName) {
		            logger.warn('ConvertConditionModeSignalsFromIdsToNames_SignalIdNotFound', {
		                modeId: mode.id,
		                targetSignalId: mode.targetSignalId,
		                targetSignalName,
		                relativeSignalId: mode.relativeSignalId,
		                relativeSignalName,
		                availableSignalIds: signals.map((s) => s.id),
		            });
		            return null;
		        }
		        const { targetSignalId, relativeSignalId, ...rest } = mode;
		        return {
		            ...rest,
		            targetSignal: targetSignalName,
		            relativeSignal: relativeSignalName,
		        };
		    })
		        .filter((mode) => mode !== null);
		}
		function convertConditionModeSignalsFromNamesToIds(modes, signals, logger) {
		    return modes
		        .map((mode) => {
		        const targetSignalId = (0, sigma_engine_shared_1.findSignalIdBySignalName)(mode.targetSignal, signals, logger);
		        const relativeSignalId = (0, sigma_engine_shared_1.findSignalIdBySignalName)(mode.relativeSignal, signals, logger);
		        if (!targetSignalId || !relativeSignalId) {
		            logger.warn('ConvertConditionModeSignalsFromNamesToIds_SignalNameNotFound', {
		                modeId: mode.id,
		                targetSignal: mode.targetSignal,
		                targetSignalId,
		                relativeSignal: mode.relativeSignal,
		                relativeSignalId,
		                availableSignalNames: signals.map((s) => s.name),
		            });
		            return null;
		        }
		        const { targetSignal, relativeSignal, ...rest } = mode;
		        return {
		            ...rest,
		            targetSignalId,
		            relativeSignalId,
		        };
		    })
		        .filter((mode) => mode !== null);
		}
		function getVoltageConditionModeDenormalizedSchemaWithInjectedSignalIds(signals) {
		    return exports.ConditionModeDenormalizedSchema.extend({
		        id: zod_1.z.string().describe('A unique id for this mode. Alphanumeric only. Example: "vcc_1p3_4p2"'),
		        characteristic: zod_1.z
		            .literal(sigma_engine_shared_2.BoundaryCharacteristic.Voltage)
		            .describe('The characteristic being measured. This is always "voltage".'),
		        minVal: zod_1.z
		            .number()
		            .nullable()
		            .describe('The minimum voltage of the mode in volts. If not provided, there is no minimum. One or both of minVal and maxVal must be provided.'),
		        nominalVal: zod_1.z
		            .number()
		            .nullable()
		            .describe('The nominal (absolute) value of this condition, if stated as a single operating point, otherwise set to null.'),
		        maxVal: zod_1.z
		            .number()
		            .nullable()
		            .describe('The maximum voltage of the mode in volts. If not provided, there is no maximum. One or both of minVal and maxVal must be provided.'),
		        targetSignalId: zod_1.z
		            .enum(signals.map((s) => s.id.toString()))
		            .describe(exports.ConditionModeDenormalizedSchema.shape.targetSignalId.description),
		        relativeSignalId: zod_1.z
		            .enum(signals.map((s) => s.id.toString()))
		            .describe(exports.ConditionModeDenormalizedSchema.shape.relativeSignalId.description),
		    })
		        .strict()
		        .refine(assertVoltageModeHasMaxMinOrNominal, {
		        message: 'Must have at least a minimum, nominal, or maximum',
		    });
		}
		function assertVoltageModeHasMaxMinOrNominal(d) {
		    return d.minVal !== undefined || d.maxVal !== undefined || d.nominalVal !== undefined;
		}
		function convertConditionModeToModeBase(mode, logger = (0, sigma_engine_shared_2.generateCompatibleConsoleLogger)()) {
		    const { minVal, maxVal, nominalVal, ...d } = mode;
		    let numerical;
		    const addNominalIfPresent = (boundary) => {
		        if (nominalVal !== null && nominalVal !== undefined) {
		            return { ...boundary, nominalValue: nominalVal };
		        }
		        return boundary;
		    };
		    if (minVal !== undefined && minVal !== null && maxVal !== undefined && maxVal !== null) {
		        if (nominalVal !== undefined && nominalVal !== null) {
		            if (nominalVal < minVal || nominalVal > maxVal) {
		                throw new Error(`Nominal value ${nominalVal} must be within the range [${minVal}, ${maxVal}] for mode ${mode.id}`);
		            }
		        }
		        numerical = addNominalIfPresent({
		            type: 'range',
		            minVal,
		            maxVal,
		            characteristic: d.characteristic,
		            minValIncluded: true,
		            maxValIncluded: true,
		        });
		    }
		    else if (minVal !== undefined && minVal !== null) {
		        numerical = addNominalIfPresent({
		            type: 'minimum',
		            value: minVal,
		            characteristic: d.characteristic,
		            valInclusive: true,
		        });
		    }
		    else if (maxVal !== undefined && maxVal !== null) {
		        numerical = addNominalIfPresent({
		            type: 'maximum',
		            value: maxVal,
		            characteristic: d.characteristic,
		            valInclusive: true,
		        });
		    }
		    else if (nominalVal !== undefined && nominalVal !== null) {
		        numerical = {
		            type: 'absolute',
		            value: nominalVal,
		            characteristic: d.characteristic,
		        };
		    }
		    else {
		        throw new Error('Need at least one of: minimum, maximum, or nominal value');
		    }
		    const boundaryClass = numerical.type === 'absolute' ?
		        sigma_engine_shared_2.BoundaryClass.IC_ElectricalCharacteristics_MeasurementPoint
		        : sigma_engine_shared_2.BoundaryClass.IC_ElectricalCharacteristics;
		    const modeBase = {
		        id: d.id,
		        preconditions: [
		            {
		                id: 0,
		                condition: {
		                    boundary: {
		                        type: sigma_engine_shared_2.BoundaryAssertionType.Numerical,
		                        numerical,
		                        target: { signal: d.targetSignalId },
		                        relative_to: { signal: d.relativeSignalId },
		                        class: boundaryClass,
		                    },
		                },
		            },
		        ],
		    };
		    const parseResult = sigma_engine_shared_2.ModeBaseSchema.safeParse(modeBase);
		    if (!parseResult.success) {
		        logger.warn('ConvertConditionModeToModeBase_ValidationFailed', {
		            modeId: mode.id,
		            errors: parseResult.error.flatten().fieldErrors,
		        });
		        return { id: d.id };
		    }
		    return parseResult.data;
		}
		function convertModeBaseToConditionModeDenormalized(mode, signals = [], restrictToCharacteristic, logger) {
		    if (!mode.preconditions || mode.preconditions.length === 0) {
		        return [];
		    }
		    const results = [];
		    for (const precondition of mode.preconditions) {
		        if ('mode' in precondition.condition) {
		            logger?.warn('ConvertModeBaseToConditionModeDenormalized_ModeReferenceConditionSkipped', {
		                modeId: mode.id,
		                preconditionId: precondition.id,
		                referencedMode: precondition.condition.mode,
		            });
		            continue;
		        }
		        if ('boundary' in precondition.condition) {
		            const boundaryCondition = precondition.condition;
		            const boundary = boundaryCondition.boundary;
		            if (boundary.type === sigma_engine_shared_2.BoundaryAssertionType.Numerical && 'numerical' in boundary) {
		                const numericalBoundary = boundary.numerical;
		                if (restrictToCharacteristic !== undefined && numericalBoundary.characteristic !== restrictToCharacteristic) {
		                    continue;
		                }
		                const targetSignalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, boundary.target);
		                if (targetSignalIds.length === 0) {
		                    throw new Error(`Expected at least one target signal ID, got 0 for mode ${mode.id}`);
		                }
		                const relativeSignalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, boundary.relative_to);
		                if (relativeSignalIds.length !== 1) {
		                    throw new Error(`Expected exactly one relative signal ID, got ${relativeSignalIds.length} for mode ${mode.id}`);
		                }
		                const relativeSignalId = relativeSignalIds[0].toString();
		                let minVal = null;
		                let maxVal = null;
		                let nominalVal = null;
		                switch (numericalBoundary.type) {
		                    case 'range':
		                        minVal = numericalBoundary.minVal;
		                        maxVal = numericalBoundary.maxVal;
		                        nominalVal = numericalBoundary.nominalValue ?? null;
		                        break;
		                    case 'minimum':
		                        minVal = numericalBoundary.value;
		                        nominalVal = numericalBoundary.nominalValue ?? null;
		                        break;
		                    case 'maximum':
		                        maxVal = numericalBoundary.value;
		                        nominalVal = numericalBoundary.nominalValue ?? null;
		                        break;
		                    case 'absolute':
		                        nominalVal = numericalBoundary.value;
		                        minVal = null;
		                        maxVal = null;
		                        break;
		                    default:
		                        throw new Error(`Unhandled boundary type: ${numericalBoundary.type}`);
		                }
		                const explodedEntries = targetSignalIds.map((targetSignalId, index) => ({
		                    id: `${mode.id}__precondition_${precondition.id}__target_${index + 1}`,
		                    characteristic: boundary.numerical.characteristic,
		                    minVal,
		                    maxVal,
		                    nominalVal,
		                    targetSignalId: targetSignalId.toString(),
		                    relativeSignalId,
		                }));
		                results.push(...explodedEntries);
		            }
		        }
		    }
		    return results;
		}
		var DriveModeVoltageConditionValidationCheckType;
		(function (DriveModeVoltageConditionValidationCheckType) {
		    DriveModeVoltageConditionValidationCheckType["TargetAndRelativeSignalShouldNotBeEqual"] = "TargetAndRelativeSignalShouldNotBeEqual";
		    DriveModeVoltageConditionValidationCheckType["TargetSignalShouldNotBeMissing"] = "TargetSignalShouldNotBeMissing";
		    DriveModeVoltageConditionValidationCheckType["RelativeSignalShouldNotBeMissing"] = "RelativeSignalShouldNotBeMissing";
		    DriveModeVoltageConditionValidationCheckType["ConditionShouldHaveAtLeastOneValue"] = "ConditionShouldHaveAtLeastOneValue";
		})(DriveModeVoltageConditionValidationCheckType || (exports.DriveModeVoltageConditionValidationCheckType = DriveModeVoltageConditionValidationCheckType = {}));
		const signal_id_remap_internal_1 = requireSignalIdRemapInternal();
		function remapSignalIdsInModes(modes, signalIdMap, opts) {
		    if (modes.length === 0) {
		        return [];
		    }
		    const cloned = JSON.parse(JSON.stringify(modes));
		    cloned.forEach((mode, i) => {
		        const preconditions = mode.preconditions ?? [];
		        preconditions.forEach((p, pi) => {
		            const cond = p.condition;
		            if (!cond || cond.type !== 'boundary' || !cond.boundary) {
		                return;
		            }
		            (0, signal_id_remap_internal_1.remapBoundaryDefinition)(cond.boundary, signalIdMap, `modes[${i}].preconditions[${pi}].condition.boundary`, opts);
		        });
		    });
		    return cloned;
		}
		
	} (conditionModeDenormalized));
	return conditionModeDenormalized;
}

var signalDriveCharacteristicsDenormalized = {};

var hasRequiredSignalDriveCharacteristicsDenormalized;

function requireSignalDriveCharacteristicsDenormalized () {
	if (hasRequiredSignalDriveCharacteristicsDenormalized) return signalDriveCharacteristicsDenormalized;
	hasRequiredSignalDriveCharacteristicsDenormalized = 1;
	Object.defineProperty(signalDriveCharacteristicsDenormalized, "__esModule", { value: true });
	signalDriveCharacteristicsDenormalized.SignalDriveCharacteristicsDenormalizedSchema = void 0;
	signalDriveCharacteristicsDenormalized.attachDriveModesToSignals = attachDriveModesToSignals;
	signalDriveCharacteristicsDenormalized.convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode = convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode;
	signalDriveCharacteristicsDenormalized.convertSignalDriveModeToSignalDriveCharacteristicsDenormalized = convertSignalDriveModeToSignalDriveCharacteristicsDenormalized;
	const zod_1 = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	signalDriveCharacteristicsDenormalized.SignalDriveCharacteristicsDenormalizedSchema = zod_1.z
	    .object({
	    signalId: zod_1.z.string().describe('The id of the signal'),
	    conditionId: zod_1.z
	        .string()
	        .nullable()
	        .describe('The id of the condition under which voltage highs/lows are reported, if applicable.'),
	    inputType: zod_1.z
	        .nativeEnum(sigma_engine_shared_1.SignalInputDriveType)
	        .nullable()
	        .describe('The input type of the signal. Set to `null` if the signal is not an input.'),
	    outputType: zod_1.z
	        .nativeEnum(sigma_engine_shared_1.SignalOutputDriveType)
	        .nullable()
	        .describe('The output type of the signal. Set to `null` if the signal is not an output.'),
	    outputHighVoltage: zod_1.z
	        .string()
	        .nullable()
	        .describe('The high voltage level of the output in SI units - e.g. VOH. Leave blank if the signal is not an output.'),
	    outputLowVoltage: zod_1.z
	        .string()
	        .nullable()
	        .describe('The low voltage level of the output in SI units - e.g. VOL. Leave blank if the signal is not an output.'),
	    inputHighVoltage: zod_1.z
	        .string()
	        .nullable()
	        .describe('The high voltage level of the input in SI units - e.g. VIH. Leave blank if the signal is not an input.'),
	    inputLowVoltage: zod_1.z
	        .string()
	        .nullable()
	        .describe('The low voltage level of the input in SI units - e.g. VIL. Leave blank if the signal is not an input.'),
	    internalPullupBiasImpedance: zod_1.z
	        .string()
	        .nullable()
	        .describe('The Impedance of the internal pullup in SI units. Set to null if the signal does not have and internal pullup.'),
	    internalPullupBiasVoltageReference: zod_1.z
	        .string()
	        .nullable()
	        .describe('The voltage level in SI units the internal pullup connects to when its a specific voltage (e.g., "1.9V", "3.3V"). Set to null if no internal pullup or if it connects to a signal rather than a fixed voltage.'),
	    internalPullupBiasSignalReference: zod_1.z
	        .string()
	        .nullable()
	        .describe('The name of the signal the internal pullup is biased by (e.g., "VCC", "VDD"). Set to null if no internal pullup or if it connects to a fixed voltage rather than a signal.'),
	    internalPulldownBiasImpedance: zod_1.z
	        .string()
	        .nullable()
	        .describe('The Impedance of the internal pulldown in SI units. Set to null if the signal does not have and internal pulldown.'),
	    internalPulldownBiasVoltageReference: zod_1.z
	        .string()
	        .nullable()
	        .describe('The voltage level in SI units the internal pulldown connects to when its a specific voltage (e.g., "1.9V", "3.3V"). Set to null if no internal pulldown or if it connects to a signal rather than a fixed voltage.'),
	    internalPulldownBiasSignalReference: zod_1.z
	        .string()
	        .nullable()
	        .describe('The name of the signal the internal pulldown is biased by (e.g., "VCC", "VDD"). Set to null if no internal pulldown or if it connects to a fixed voltage rather than a signal.'),
	    outputImpedance: zod_1.z
	        .string()
	        .nullable()
	        .describe('The output impedance of the signal in SI units. Leave blank if the signal is not an output.'),
	})
	    .strict();
	function attachDriveModesToSignals(signals, driveCharacteristics, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    return signals.map((signal) => {
	        const supportedDriveModes = [];
	        for (const driveCharacteristic of driveCharacteristics.filter((char) => char.signalId === signal.id)) {
	            try {
	                supportedDriveModes.push(convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode(driveCharacteristic));
	            }
	            catch (error) {
	                logger.warn('AttachDriveModesToSignals_SkippedDriveCharacteristic', {
	                    signalId: signal.id,
	                    signalName: signal.name,
	                    error: error instanceof Error ? error.message : String(error),
	                });
	            }
	        }
	        return supportedDriveModes.length > 0 ? { ...signal, supportedDriveModes } : signal;
	    });
	}
	function convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode(characteristics, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    const driveType = characteristics.inputType ?? characteristics.outputType;
	    if (!driveType) {
	        throw new Error(`Signal ${characteristics.signalId} has neither input nor output type specified`);
	    }
	    const parameters = {};
	    if (characteristics.outputHighVoltage !== null) {
	        parameters.outputHighVoltage = characteristics.outputHighVoltage;
	    }
	    if (characteristics.outputLowVoltage !== null) {
	        parameters.outputLowVoltage = characteristics.outputLowVoltage;
	    }
	    if (characteristics.inputHighVoltage !== null) {
	        parameters.inputHighVoltage = characteristics.inputHighVoltage;
	    }
	    if (characteristics.inputLowVoltage !== null) {
	        parameters.inputLowVoltage = characteristics.inputLowVoltage;
	    }
	    if (characteristics.outputImpedance !== null) {
	        parameters.outputImpedance = characteristics.outputImpedance;
	    }
	    if (characteristics.internalPullupBiasImpedance !== null) {
	        const pullupBias = characteristics.internalPullupBiasSignalReference !== null ?
	            {
	                type: 'signal',
	                impedance: characteristics.internalPullupBiasImpedance,
	                signalReference: { signal_name: characteristics.internalPullupBiasSignalReference },
	            }
	            : {
	                type: 'voltage',
	                impedance: characteristics.internalPullupBiasImpedance,
	                voltageReference: sigma_engine_shared_1.Utils.parseNumericalSIValue(characteristics.internalPullupBiasVoltageReference ?? '0', sigma_engine_shared_1.CommonParameterUnits.voltage) ?? parseFloat(characteristics.internalPullupBiasVoltageReference ?? '0'),
	            };
	        parameters.internalPullupBias = pullupBias;
	    }
	    if (characteristics.internalPulldownBiasImpedance !== null) {
	        const pulldownBias = characteristics.internalPulldownBiasSignalReference !== null ?
	            {
	                type: 'signal',
	                impedance: characteristics.internalPulldownBiasImpedance,
	                signalReference: { signal_name: characteristics.internalPulldownBiasSignalReference },
	            }
	            : {
	                type: 'voltage',
	                impedance: characteristics.internalPulldownBiasImpedance,
	                voltageReference: sigma_engine_shared_1.Utils.parseNumericalSIValue(characteristics.internalPulldownBiasVoltageReference ?? '0', sigma_engine_shared_1.CommonParameterUnits.voltage) ?? parseFloat(characteristics.internalPulldownBiasVoltageReference ?? '0'),
	            };
	        parameters.internalPulldownBias = pulldownBias;
	    }
	    const conditions = [];
	    if (characteristics.conditionId !== null) {
	        conditions.push({
	            condition: {
	                mode: characteristics.conditionId,
	                mode_state: true,
	            },
	        });
	    }
	    const signalDriveMode = {
	        type: driveType,
	    };
	    if (Object.keys(parameters).length > 0) {
	        signalDriveMode.parameters = parameters;
	    }
	    if (conditions.length > 0) {
	        signalDriveMode.conditions = conditions;
	    }
	    const parseResult = sigma_engine_shared_1.DesignModels.SignalDriveModeDefinitionSchema.safeParse(signalDriveMode);
	    if (!parseResult.success) {
	        logger.warn('ConvertSignalDriveCharacteristicsDenormalized_InvalidFieldsDropped', {
	            signalId: characteristics.signalId,
	            errors: parseResult.error.flatten().fieldErrors,
	        });
	        return { type: driveType };
	    }
	    return parseResult.data;
	}
	function convertSignalDriveModeToSignalDriveCharacteristicsDenormalized(driveMode, signalId) {
	    const parameters = driveMode.parameters ?? {};
	    const isInputType = Object.values(sigma_engine_shared_1.SignalInputDriveType).includes(driveMode.type);
	    const isOutputType = Object.values(sigma_engine_shared_1.SignalOutputDriveType).includes(driveMode.type);
	    const conditionId = driveMode.conditions?.[0]?.condition && 'mode' in driveMode.conditions[0].condition ?
	        driveMode.conditions[0].condition.mode
	        : null;
	    const convertToStringOrNull = (value) => {
	        if (value === undefined || value === null) {
	            return null;
	        }
	        return typeof value === 'string' ? value : value.toString();
	    };
	    const pullupBias = parameters.internalPullupBias;
	    const pulldownBias = parameters.internalPulldownBias;
	    return {
	        signalId,
	        conditionId,
	        inputType: isInputType ? driveMode.type : null,
	        outputType: isOutputType ? driveMode.type : null,
	        outputHighVoltage: convertToStringOrNull(parameters.outputHighVoltage),
	        outputLowVoltage: convertToStringOrNull(parameters.outputLowVoltage),
	        inputHighVoltage: convertToStringOrNull(parameters.inputHighVoltage),
	        inputLowVoltage: convertToStringOrNull(parameters.inputLowVoltage),
	        internalPullupBiasImpedance: pullupBias?.impedance ?? null,
	        internalPullupBiasVoltageReference: pullupBias?.type === 'voltage' ? pullupBias.voltageReference.toString() : null,
	        internalPullupBiasSignalReference: pullupBias?.type === 'signal' ?
	            Array.isArray(pullupBias.signalReference.signal_name) ?
	                (pullupBias.signalReference.signal_name[0] ?? null)
	                : (pullupBias.signalReference.signal_name ?? null)
	            : null,
	        internalPulldownBiasImpedance: pulldownBias?.impedance ?? null,
	        internalPulldownBiasVoltageReference: pulldownBias?.type === 'voltage' ? pulldownBias.voltageReference.toString() : null,
	        internalPulldownBiasSignalReference: pulldownBias?.type === 'signal' ?
	            Array.isArray(pulldownBias.signalReference.signal_name) ?
	                (pulldownBias.signalReference.signal_name[0] ?? null)
	                : (pulldownBias.signalReference.signal_name ?? null)
	            : null,
	        outputImpedance: parameters.outputImpedance ?? null,
	    };
	}
	
	return signalDriveCharacteristicsDenormalized;
}

var conditionCanonicalization = {};

var hasRequiredConditionCanonicalization;

function requireConditionCanonicalization () {
	if (hasRequiredConditionCanonicalization) return conditionCanonicalization;
	hasRequiredConditionCanonicalization = 1;
	Object.defineProperty(conditionCanonicalization, "__esModule", { value: true });
	conditionCanonicalization.canonicalizeBoundaryDefinition = canonicalizeBoundaryDefinition;
	conditionCanonicalization.canonicalizeModeContent = canonicalizeModeContent;
	conditionCanonicalization.canonicalizeModeConditions = canonicalizeModeConditions;
	conditionCanonicalization.canonicalizeModeRefById = canonicalizeModeRefById;
	const sigma_engine_shared_1 = requireDist$1();
	const generate_readable_id_1 = requireGenerateReadableId();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const CONDITION_ID_SEPARATOR = ',';
	const SIGNAL_STATE_SUFFIX = '_state';
	var UnfoldableMode;
	(function (UnfoldableMode) {
	    UnfoldableMode["Absent"] = "absent_mode";
	    UnfoldableMode["Cyclic"] = "cyclic_mode";
	    UnfoldableMode["Contentless"] = "contentless_mode";
	})(UnfoldableMode || (UnfoldableMode = {}));
	function unfoldableModeId(reason, modeId) {
	    return sanitizeConditionId(`<${reason}:${modeId}>`);
	}
	function sanitizeConditionId(id, logger) {
	    if (!id.includes(CONDITION_ID_SEPARATOR)) {
	        return id;
	    }
	    logger?.warn('SanitizeConditionId_SeparatorInConditionId', { conditionId: id });
	    return id.split(CONDITION_ID_SEPARATOR).join('_');
	}
	function resolveTargetNames(signals, target) {
	    if (target === undefined) {
	        return [];
	    }
	    const nameById = new Map(signals.map((signal) => [String(signal.id), signal.name]));
	    return (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, target)
	        .map((id) => nameById.get(String(id)) ?? String(id))
	        .map(signal_name_normalization_1.normalizeSignalNameForComparison)
	        .sort();
	}
	function boundsOf(numerical) {
	    const nominal = numerical.nominalValue ?? null;
	    switch (numerical.type) {
	        case 'range':
	            return { min: numerical.minVal, nominal, max: numerical.maxVal };
	        case 'minimum':
	            return { min: numerical.value, nominal, max: null };
	        case 'maximum':
	            return { min: null, nominal, max: numerical.value };
	        case 'absolute':
	            return { min: null, nominal: numerical.value, max: null };
	    }
	}
	function canonicalizeBoundaryDefinition(definition, signals, logger) {
	    const targetName = resolveTargetNames(signals, definition.target).join('+');
	    if (!(0, sigma_engine_shared_1.isBoundaryNumericalDefinition)(definition)) {
	        return sanitizeConditionId(`${targetName}${SIGNAL_STATE_SUFFIX}`, logger);
	    }
	    const { min, nominal, max } = boundsOf(definition.numerical);
	    if (min === null && nominal === null && max === null) {
	        return sanitizeConditionId(`${targetName}${SIGNAL_STATE_SUFFIX}`, logger);
	    }
	    return sanitizeConditionId((0, generate_readable_id_1.generateReadableConditionModeId)(targetName, min, nominal, max), logger);
	}
	function canonicalizeModeContent(mode, modes, signals, seen = new Set(), logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    const nextSeen = new Set(seen).add(mode.id);
	    const preconditions = mode.preconditions ?? [];
	    if (preconditions.length === 0) {
	        logger.warn('CanonicalizeModeContent_NoPreconditions', { modeId: mode.id });
	        return unfoldableModeId(UnfoldableMode.Contentless, mode.id);
	    }
	    if (preconditions.length > 1) {
	        logger.warn('CanonicalizeModeContent_MultiplePreconditions', {
	            modeId: mode.id,
	            preconditionCount: preconditions.length,
	        });
	    }
	    const ids = preconditions.map((precondition) => canonicalizeCondition(precondition.condition, modes, signals, nextSeen, logger));
	    return [...new Set(ids)].sort().join(CONDITION_ID_SEPARATOR);
	}
	function canonicalizeCondition(condition, modes, signals, seen, logger) {
	    if ((0, sigma_engine_shared_1.isModeReferenceCondition)(condition)) {
	        if (seen.has(condition.mode)) {
	            logger.warn('CanonicalizeCondition_CyclicModeReference', { modeId: condition.mode });
	            return unfoldableModeId(UnfoldableMode.Cyclic, condition.mode);
	        }
	        const referenced = modes.find((mode) => mode.id === condition.mode);
	        if (referenced === undefined) {
	            logger.warn('CanonicalizeCondition_AbsentModeReference', { modeId: condition.mode });
	            return unfoldableModeId(UnfoldableMode.Absent, condition.mode);
	        }
	        return canonicalizeModeContent(referenced, modes, signals, seen, logger);
	    }
	    if ((0, sigma_engine_shared_1.isModeSignalLogicalStateCondition)(condition)) {
	        const signal = signals.find((candidate) => String(candidate.id) === String(condition.signalId));
	        const name = (0, signal_name_normalization_1.normalizeSignalNameForComparison)(signal?.name ?? String(condition.signalId));
	        return sanitizeConditionId(`${name}${SIGNAL_STATE_SUFFIX}`, logger);
	    }
	    return canonicalizeBoundaryDefinition(condition.boundary, signals, logger);
	}
	function canonicalizeModeConditions(conditions, modes, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    if (conditions === undefined || conditions.length === 0) {
	        return '';
	    }
	    const ids = conditions.map((condition) => canonicalizeCondition(condition, modes, signals, new Set(), logger));
	    return [...new Set(ids)].sort().join(CONDITION_ID_SEPARATOR);
	}
	function canonicalizeModeRefById(modeId, modes, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    if (modeId === null || modeId === undefined || modeId === '') {
	        return '';
	    }
	    const mode = modes.find((candidate) => candidate.id === modeId);
	    if (mode === undefined) {
	        logger.warn('CanonicalizeModeRefById_AbsentMode', { modeId });
	        return unfoldableModeId(UnfoldableMode.Absent, modeId);
	    }
	    return canonicalizeModeContent(mode, modes, signals, new Set(), logger);
	}
	
	return conditionCanonicalization;
}

var hasRequiredGeneralPackageSlices;

function requireGeneralPackageSlices () {
	if (hasRequiredGeneralPackageSlices) return generalPackageSlices;
	hasRequiredGeneralPackageSlices = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BoundaryConditionKind = exports.GENERAL_VALUE_SLICE_KEYS = exports.GENERAL_CONSOLIDATION_SLICE_KEYS = exports.FUNCTION_SIGNAL_MAPPING_SLICE_KEY = exports.FUNCTIONS_SLICE_KEY = exports.RESOURCES_SLICE_KEY = exports.DRIVE_MODES_SLICE_KEY = exports.OC_BOUNDARIES_SLICE_KEY = exports.AMR_BOUNDARIES_SLICE_KEY = exports.SIGNAL_TIES_SLICE_KEY = exports.FLOATING_BEHAVIOUR_SLICE_KEY = exports.PIN_ASSIGNMENTS_SLICE_KEY = exports.PINOUT_SLICE_KEY = exports.SIGNAL_ATTRIBUTES_SLICE_KEY = exports.MODES_SLICE_KEY = exports.SIGNAL_SET_SLICE_KEY = void 0;
		exports.buildSignalSetRows = buildSignalSetRows;
		exports.buildSignalAttributeRows = buildSignalAttributeRows;
		exports.buildPinoutRows = buildPinoutRows;
		exports.buildPinAssignmentRows = buildPinAssignmentRows;
		exports.buildFloatingBehaviourRows = buildFloatingBehaviourRows;
		exports.buildSignalTieRows = buildSignalTieRows;
		exports.buildBoundaryConditionRows = buildBoundaryConditionRows;
		exports.buildDriveModeRows = buildDriveModeRows;
		exports.buildModeReferenceLabels = buildModeReferenceLabels;
		exports.buildModeRows = buildModeRows;
		exports.buildResourceRows = buildResourceRows;
		exports.buildFunctionRows = buildFunctionRows;
		exports.buildFunctionSignalMappingRows = buildFunctionSignalMappingRows;
		exports.createGeneralReviewSlices = createGeneralReviewSlices;
		const sigma_engine_shared_1 = requireDist$1();
		const deepEqual = requireFastDeepEqual();
		const boundary_conditions_denormalized_1 = requireBoundaryConditionsDenormalized();
		const condition_mode_denormalized_1 = requireConditionModeDenormalized();
		const component_function_denormalized_1 = requireComponentFunctionDenormalized();
		const signal_attributes_denormalized_1 = requireSignalAttributesDenormalized();
		const signal_drive_characteristics_denormalized_1 = requireSignalDriveCharacteristicsDenormalized();
		const SignalTies_1 = requireSignalTies();
		const signal_name_normalization_1 = requireSignalNameNormalization();
		const si_comparison_1 = requireSiComparison();
		const comparison_spec_1 = requireComparisonSpec();
		const consensus_assembler_1 = requireConsensusAssembler();
		const multi_reviewer_consensus_service_1 = requireMultiReviewerConsensus_service();
		const boundary_conditions_comparison_1 = requireBoundaryConditions_comparison();
		const drive_modes_comparison_1 = requireDriveModes_comparison();
		const floating_behaviour_comparison_1 = requireFloatingBehaviour_comparison();
		const functions_comparison_1 = requireFunctions_comparison();
		const function_signal_mapping_comparison_1 = requireFunctionSignalMapping_comparison();
		const modes_comparison_1 = requireModes_comparison();
		const pin_assignments_comparison_1 = requirePinAssignments_comparison();
		const pinout_comparison_1 = requirePinout_comparison();
		const resources_comparison_1 = requireResources_comparison();
		const sigma_model_parameters_comparison_1 = requireSigmaModelParameters_comparison();
		const scalar_comparison_1 = requireScalar_comparison();
		const signal_attributes_comparison_1 = requireSignalAttributes_comparison();
		const signal_set_comparison_1 = requireSignalSet_comparison();
		const signal_ties_comparison_1 = requireSignalTies_comparison();
		const slice_contracts_1 = requireSliceContracts();
		const slice_key_1 = requireSliceKey();
		const slice_scoped_logger_1 = requireSliceScopedLogger();
		const condition_canonicalization_1 = requireConditionCanonicalization();
		exports.SIGNAL_SET_SLICE_KEY = slice_key_1.SliceKey.SignalSet;
		exports.MODES_SLICE_KEY = slice_key_1.SliceKey.Modes;
		exports.SIGNAL_ATTRIBUTES_SLICE_KEY = slice_key_1.SliceKey.SignalAttributes;
		exports.PINOUT_SLICE_KEY = slice_key_1.SliceKey.Pinout;
		exports.PIN_ASSIGNMENTS_SLICE_KEY = slice_key_1.SliceKey.PinAssignments;
		exports.FLOATING_BEHAVIOUR_SLICE_KEY = slice_key_1.SliceKey.FloatingBehaviour;
		exports.SIGNAL_TIES_SLICE_KEY = slice_key_1.SliceKey.SignalTies;
		exports.AMR_BOUNDARIES_SLICE_KEY = slice_key_1.SliceKey.AmrBoundaries;
		exports.OC_BOUNDARIES_SLICE_KEY = slice_key_1.SliceKey.OcBoundaries;
		exports.DRIVE_MODES_SLICE_KEY = slice_key_1.SliceKey.DriveModes;
		exports.RESOURCES_SLICE_KEY = slice_key_1.SliceKey.Resources;
		exports.FUNCTIONS_SLICE_KEY = slice_key_1.SliceKey.Functions;
		exports.FUNCTION_SIGNAL_MAPPING_SLICE_KEY = slice_key_1.SliceKey.FunctionSignalMapping;
		exports.GENERAL_CONSOLIDATION_SLICE_KEYS = [exports.SIGNAL_SET_SLICE_KEY, exports.MODES_SLICE_KEY];
		exports.GENERAL_VALUE_SLICE_KEYS = [
		    exports.SIGNAL_ATTRIBUTES_SLICE_KEY,
		    exports.PINOUT_SLICE_KEY,
		    exports.FLOATING_BEHAVIOUR_SLICE_KEY,
		    exports.SIGNAL_TIES_SLICE_KEY,
		    exports.AMR_BOUNDARIES_SLICE_KEY,
		    exports.OC_BOUNDARIES_SLICE_KEY,
		    exports.DRIVE_MODES_SLICE_KEY,
		    exports.RESOURCES_SLICE_KEY,
		    exports.FUNCTIONS_SLICE_KEY,
		    exports.FUNCTION_SIGNAL_MAPPING_SLICE_KEY,
		    slice_contracts_1.PARAMETERS_SLICE_KEY,
		];
		const projectionLogger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)();
		class GeneralReviewSlice {
		    resolveSpec(_payloads) {
		        return this.spec;
		    }
		    normalizeValue(column, value, row) {
		        return this.resolveSpec([]).normalize(column, value, row);
		    }
		    admitsValue(column, value) {
		        return this.resolveSpec([]).admitsValue(column, value);
		    }
		    rowKeyOf(row) {
		        return this.resolveSpec([]).rowKeyOf(row);
		    }
		    rowCellsFromKey(rowKey) {
		        return this.resolveSpec([]).rowCellsFromKey(rowKey);
		    }
		    withRows(table, rows) {
		        const spec = this.resolveSpec([]);
		        const existing = table === undefined ? [] : spec.toRows(table);
		        const present = new Set(existing.map((row) => spec.rowKeyOf(row)));
		        const added = rows.filter((row) => !present.has(spec.rowKeyOf(row)));
		        return added.length === 0 ? table : spec.fromRows([...existing, ...added]);
		    }
		    run(submissions, policies, logger) {
		        const scoped = new slice_scoped_logger_1.SliceScopedLogger(this.sliceKey, logger ?? projectionLogger);
		        const service = new multi_reviewer_consensus_service_1.MultiReviewerConsensusService(scoped);
		        const projected = submissions.map(([reviewerId, payload]) => [reviewerId, this.buildData(payload, scoped)]);
		        const spec = this.resolveSpec(submissions.map(([, payload]) => payload));
		        const membershipHoldings = spec.membershipScopeColumns === undefined ?
		            undefined
		            : new Map(submissions.map(([reviewerId, payload]) => [
		                reviewerId,
		                new Set(payload.signals.map((signal) => (0, signal_name_normalization_1.normalizeSignalNameForComparison)(signal.name))),
		            ]));
		        return service.run(projected, {
		            spec,
		            confidencePolicy: policies.confidencePolicy,
		            triagePolicy: policies.triagePolicy,
		            assembler: new consensus_assembler_1.ConsensusAssembler(),
		            membershipHoldings,
		        });
		    }
		    autoResolveColumnNames() {
		        return [...this.spec.autoResolveColumns.keys()].map(String);
		    }
		    keyColumnNames() {
		        const alignment = this.spec.alignment;
		        return alignment instanceof comparison_spec_1.CellAlignment ? alignment.keyColumns.map(String) : [];
		    }
		    valueColumnNames() {
		        const alignment = this.spec.alignment;
		        return alignment instanceof comparison_spec_1.CellAlignment ? alignment.valueColumns.map(String) : [];
		    }
		}
		function signalNameById(payload) {
		    return new Map(payload.signals.map((signal) => [String(signal.id), signal.name]));
		}
		function buildSignalSetRows(payload) {
		    return payload.signals.map((signal) => {
		        const attributes = (0, signal_attributes_denormalized_1.getSignalAttributesInDenormalizedFormat)(signal);
		        const flags = [
		            attributes.direction ?? '',
		            attributes.power ? 'power' : '',
		            attributes.ground ? 'ground' : '',
		            attributes.analog ? 'analog' : '',
		            attributes.digital ? 'digital' : '',
		            attributes.exposedPad ? 'exposed pad' : '',
		            attributes.noConnect ? 'no connect' : '',
		            attributes.activeLow ? 'active low' : '',
		        ].filter((flag) => flag !== '');
		        return {
		            signalId: String(signal.id),
		            signalName: signal.name,
		            pins: [...(payload.pinout[String(signal.id)] ?? [])],
		            attributes: flags.join(', '),
		            description: attributes.description ?? '',
		        };
		    });
		}
		function buildSignalAttributeRows(payload) {
		    return payload.signals.map((signal) => ({
		        ...(0, signal_attributes_denormalized_1.getSignalAttributesInDenormalizedFormat)(signal),
		        modelSignal: signal.modelSignal === undefined ? null : String(signal.modelSignal),
		    }));
		}
		function buildPinoutRows(payload) {
		    const nameById = signalNameById(payload);
		    const pinsByName = new Map();
		    for (const [signalId, pinIds] of Object.entries(payload.pinout)) {
		        const signalName = nameById.get(signalId);
		        if (signalName === undefined) {
		            continue;
		        }
		        const list = pinsByName.get(signalName) ?? [];
		        list.push(...pinIds);
		        pinsByName.set(signalName, list);
		    }
		    return [...pinsByName.entries()].map(([signalName, pins]) => ({ signalName, pins: (0, pinout_comparison_1.joinPinList)(pins) }));
		}
		function buildPinAssignmentRows(payload) {
		    const nameById = signalNameById(payload);
		    const rows = [];
		    for (const [signalId, pinIds] of Object.entries(payload.pinout)) {
		        const signalName = nameById.get(signalId);
		        if (signalName === undefined) {
		            continue;
		        }
		        for (const pinId of pinIds) {
		            rows.push({ pinId, signalName });
		        }
		    }
		    return rows;
		}
		function buildFloatingBehaviourRows(payload) {
		    return payload.signals.map((signal) => ({
		        signalName: signal.name,
		        floatingBehaviour: signal.floatingBehavior ?? '',
		    }));
		}
		function buildSignalTieRows(payload, logger = projectionLogger) {
		    const directionRule = new SignalTies_1.SignalTieDirectionRule(payload.signals.map(signal_attributes_denormalized_1.getSignalAttributesInDenormalizedFormat));
		    const rows = [];
		    for (const signal of payload.signals) {
		        if (!signal.ties) {
		            continue;
		        }
		        for (const externalComponent of signal.ties) {
		            for (const single of (0, SignalTies_1.splitExternalComponentByConnection)(externalComponent)) {
		                const flatTie = (0, SignalTies_1.convertExternalComponentToFlatSignalTie)(single, payload.signals, logger);
		                if (flatTie === null) {
		                    continue;
		                }
		                const toSignalName = sigma_engine_shared_1.DesignModels.findSignalNameBySignalId(flatTie.toSignalId, payload.signals, logger) ?? flatTie.toSignalId;
		                const values = (0, SignalTies_1.flatTieToRowValues)(flatTie);
		                rows.push(directionRule.orientEnds({
		                    signalName: signal.name,
		                    toSignalName,
		                    connectionType: flatTie.connectionType,
		                    componentType: values.componentType,
		                    componentValueAbs: values.componentValueAbs,
		                    componentValueMin: values.componentValueMin,
		                    componentValueMax: values.componentValueMax,
		                    componentValueVariance: values.componentValueVariance,
		                    polarity: values.polarity,
		                }));
		            }
		        }
		    }
		    return rows;
		}
		var BoundaryConditionKind;
		(function (BoundaryConditionKind) {
		    BoundaryConditionKind["Amr"] = "amr";
		    BoundaryConditionKind["Oc"] = "oc";
		})(BoundaryConditionKind || (exports.BoundaryConditionKind = BoundaryConditionKind = {}));
		const BOUND_MERGE_RULES = [
		    { column: 'minimum', keep: 'highest' },
		    { column: 'nominal', keep: 'lowest' },
		    { column: 'maximum', keep: 'lowest' },
		];
		function numericBound(value, characteristic) {
		    const parsed = Number((0, si_comparison_1.formatSIValue)(value, characteristic));
		    return Number.isFinite(parsed) ? parsed : null;
		}
		function resolveConflictingBound(existing, incoming, characteristic, keep) {
		    const existingMagnitude = numericBound(existing, characteristic);
		    const incomingMagnitude = numericBound(incoming, characteristic);
		    if (existingMagnitude === null || incomingMagnitude === null) {
		        const existingWins = existing <= incoming;
		        return {
		            kept: existingWins ? existing : incoming,
		            discarded: existingWins ? incoming : existing,
		            rule: 'lexicographic',
		        };
		    }
		    const existingWins = keep === 'highest' ? existingMagnitude >= incomingMagnitude : existingMagnitude <= incomingMagnitude;
		    return { kept: existingWins ? existing : incoming, discarded: existingWins ? incoming : existing, rule: keep };
		}
		function mergeBoundaryRows(existing, incoming, spec, logger) {
		    const merged = { ...existing };
		    for (const { column, keep } of BOUND_MERGE_RULES) {
		        const candidate = incoming[column];
		        if (candidate === null) {
		            continue;
		        }
		        const held = merged[column];
		        if (held === null) {
		            merged[column] = candidate;
		            continue;
		        }
		        if (spec.normalize(column, held, merged) === spec.normalize(column, candidate, incoming)) {
		            continue;
		        }
		        const resolution = resolveConflictingBound(held, candidate, incoming.characteristic, keep);
		        logger.warn('BuildBoundaryConditionRows_ConflictingBound', {
		            signalName: incoming.signalName,
		            relativeToSignalName: incoming.relativeToSignalName,
		            characteristic: incoming.characteristic,
		            conditions: incoming.conditions,
		            column,
		            kept: resolution.kept,
		            discarded: resolution.discarded,
		            rule: resolution.rule,
		        });
		        merged[column] = resolution.kept;
		    }
		    return merged;
		}
		function unionRowsByKey(rows, spec, logger) {
		    const byKey = new Map();
		    for (const row of rows) {
		        const key = spec.rowKeyOf(row);
		        const existing = byKey.get(key);
		        byKey.set(key, existing === undefined ? row : mergeBoundaryRows(existing, row, spec, logger));
		    }
		    return [...byKey.values()];
		}
		function declaredRelativeSignalIds(boundary) {
		    const relativeTo = boundary.boundary.relative_to;
		    if (relativeTo === undefined) {
		        return [];
		    }
		    const entries = Array.isArray(relativeTo) ? relativeTo : [relativeTo];
		    return entries.flatMap((entry) => (entry.signal === undefined ? [] : [String(entry.signal)]));
		}
		function collapseRelativeSignalName(entry, logger) {
		    const references = [
		        entry.minimumRelativeToSignalName,
		        entry.nominalRelativeToSignalName,
		        entry.maximumRelativeToSignalName,
		    ].filter((reference) => reference !== null && reference !== undefined && reference !== '');
		    const distinct = [...new Set(references)];
		    if (distinct.length > 1) {
		        logger.warn('BuildBoundaryConditionRows_DivergentRelativeSignals', {
		            signalName: entry.signalName,
		            characteristic: entry.characteristic,
		            references: distinct,
		        });
		    }
		    return references[0] ?? '';
		}
		function buildBoundaryConditionRows(payload, kind, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
		    const rows = [];
		    for (const boundary of payload.boundaries) {
		        const denormalized = kind === BoundaryConditionKind.Amr ?
		            (0, boundary_conditions_denormalized_1.convertBoundariesToAmrDenormalized)([boundary], payload.signals).map((entry) => ({
		                ...entry,
		                nominal: null,
		                nominalRelativeToSignalName: null,
		            }))
		            : (0, boundary_conditions_denormalized_1.convertBoundariesToOcDenormalized)([boundary], payload.signals);
		        if (denormalized.length === 0) {
		            continue;
		        }
		        const conditions = (0, condition_canonicalization_1.canonicalizeModeConditions)(boundary.conditions, payload.modes, payload.signals, logger);
		        const declaresReference = declaredRelativeSignalIds(boundary).length > 0;
		        for (const entry of denormalized) {
		            const relativeToSignalName = collapseRelativeSignalName(entry, logger);
		            if (declaresReference && relativeToSignalName === '') {
		                logger.warn('BuildBoundaryConditionRows_UnresolvedRelativeSignal', {
		                    signalName: entry.signalName,
		                    characteristic: entry.characteristic,
		                    declaredRelativeSignalIds: declaredRelativeSignalIds(boundary),
		                });
		            }
		            rows.push({
		                signalName: entry.signalName,
		                relativeToSignalName,
		                characteristic: entry.characteristic,
		                conditions,
		                minimum: entry.minimum ?? null,
		                nominal: entry.nominal ?? null,
		                maximum: entry.maximum ?? null,
		                ...(boundary.conditions === undefined ? {} : { sourceConditions: boundary.conditions }),
		            });
		        }
		    }
		    return unionRowsByKey(rows, new boundary_conditions_comparison_1.BoundaryConditionsComparison(), logger);
		}
		function driveModeModeConditions(driveMode, signalName, logger) {
		    const conditions = [];
		    for (const entry of driveMode.conditions ?? []) {
		        const condition = entry.condition;
		        if ((0, sigma_engine_shared_1.isModeReferenceCondition)(condition) ||
		            (0, sigma_engine_shared_1.isModeSignalLogicalStateCondition)(condition) ||
		            'boundary' in condition) {
		            conditions.push(condition);
		            continue;
		        }
		        logger.warn('BuildDriveModeRows_NonModeConditionExcluded', {
		            signalName,
		            driveModeType: driveMode.type,
		            conditionKeys: Object.keys(condition),
		        });
		    }
		    return conditions;
		}
		function mergeDriveModeRows(existing, incoming, spec, logger) {
		    const merged = { ...existing };
		    for (const column of drive_modes_comparison_1.DRIVE_MODE_VALUE_COLUMNS) {
		        const candidate = incoming[column];
		        if (candidate === null || candidate === undefined) {
		            continue;
		        }
		        const held = merged[column];
		        if (held === null || held === undefined) {
		            merged[column] = candidate;
		            continue;
		        }
		        if (spec.normalize(column, held, merged) === spec.normalize(column, candidate, incoming)) {
		            continue;
		        }
		        logger.warn('BuildDriveModeRows_ConflictingValue', {
		            signalName: incoming.signalName,
		            inputType: incoming.inputType,
		            outputType: incoming.outputType,
		            conditionId: incoming.conditionId,
		            column,
		            kept: held,
		            discarded: candidate,
		        });
		    }
		    if (!deepEqual(existing.sourceConditions, incoming.sourceConditions)) {
		        logger.warn('BuildDriveModeRows_DivergentSourceConditions', {
		            signalName: incoming.signalName,
		            inputType: incoming.inputType,
		            outputType: incoming.outputType,
		            conditionId: incoming.conditionId,
		        });
		    }
		    return merged;
		}
		function unionDriveModeRowsByKey(rows, spec, logger) {
		    const byKey = new Map();
		    for (const row of rows) {
		        const key = spec.rowKeyOf(row);
		        const existing = byKey.get(key);
		        byKey.set(key, existing === undefined ? row : mergeDriveModeRows(existing, row, spec, logger));
		    }
		    return [...byKey.values()];
		}
		function buildDriveModeRows(payload, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
		    const spec = new drive_modes_comparison_1.DriveModesComparison();
		    const nulledNeverReviewedColumns = Object.fromEntries(drive_modes_comparison_1.NEVER_REVIEWED_DRIVE_MODE_COLUMNS.map((column) => [column, null]));
		    const sourceRows = payload.signals.flatMap((signal) => (signal.supportedDriveModes ?? []).map((driveMode) => {
		        const row = (0, signal_drive_characteristics_denormalized_1.convertSignalDriveModeToSignalDriveCharacteristicsDenormalized)(driveMode, signal.name);
		        const conditions = driveModeModeConditions(driveMode, signal.name, logger);
		        return {
		            ...row,
		            ...nulledNeverReviewedColumns,
		            conditionId: (0, condition_canonicalization_1.canonicalizeModeConditions)(conditions, payload.modes, payload.signals, logger),
		            ...(driveMode.conditions === undefined ? {} : { sourceConditions: driveMode.conditions }),
		        };
		    }));
		    return spec.fromRows(unionDriveModeRowsByKey(spec.toRows(sourceRows), spec, logger));
		}
		function buildModeReferenceLabels(payload) {
		    const nameById = signalNameById(payload);
		    const labels = new Map();
		    const add = (modeId, label) => {
		        const list = labels.get(modeId) ?? [];
		        if (!list.includes(label)) {
		            list.push(label);
		            labels.set(modeId, list);
		        }
		    };
		    for (const boundary of payload.boundaries) {
		        const kind = boundary.boundary.class === sigma_engine_shared_1.BoundaryClass.IC_AbsoluteMaximumRating ? 'AMR' : 'OC';
		        const targetNames = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(payload.signals, boundary.boundary.target)
		            .map((id) => nameById.get(String(id)) ?? String(id))
		            .join('/');
		        for (const condition of boundary.conditions ?? []) {
		            if ((0, sigma_engine_shared_1.isModeReferenceCondition)(condition)) {
		                add(condition.mode, `${kind}: ${targetNames}`);
		            }
		        }
		    }
		    for (const signal of payload.signals) {
		        for (const driveMode of signal.supportedDriveModes ?? []) {
		            for (const driveCondition of driveMode.conditions ?? []) {
		                const condition = driveCondition.condition;
		                if (condition !== null && typeof condition === 'object' && (0, sigma_engine_shared_1.isModeReferenceCondition)(condition)) {
		                    add(condition.mode, `drive mode: ${signal.name}`);
		                }
		            }
		        }
		    }
		    for (const resource of payload.resources) {
		        for (const condition of resource.conditions ?? []) {
		            if ((0, sigma_engine_shared_1.isModeReferenceCondition)(condition)) {
		                add(condition.mode, `resource: ${resource.name}`);
		            }
		        }
		    }
		    return labels;
		}
		function buildModeRows(payload, logger = projectionLogger) {
		    const referenceLabels = buildModeReferenceLabels(payload);
		    return payload.modes.flatMap((mode) => {
		        try {
		            const denormalized = (0, condition_mode_denormalized_1.convertModeBaseToConditionModeDenormalized)(mode, payload.signals);
		            return (0, condition_mode_denormalized_1.convertConditionModeSignalsFromIdsToNames)(denormalized, payload.signals, logger).map((entry) => ({
		                id: mode.id,
		                characteristic: entry.characteristic,
		                minVal: entry.minVal,
		                nominalVal: entry.nominalVal,
		                maxVal: entry.maxVal,
		                targetSignal: entry.targetSignal,
		                relativeSignal: entry.relativeSignal,
		                referencedBy: (referenceLabels.get(mode.id) ?? []).join('; '),
		            }));
		        }
		        catch {
		            logger.warn('BuildModeRows_UnflattenableMode', { modeId: mode.id });
		            return [];
		        }
		    });
		}
		function buildResourceRows(payload, logger = projectionLogger) {
		    return payload.resources.map((resource) => ({
		        name: resource.name,
		        mustBeConsumed: resource.mustBeConsumed ?? false,
		        conditions: (0, condition_canonicalization_1.canonicalizeModeConditions)(resource.conditions, payload.modes, payload.signals, logger),
		        ...(resource.conditions === undefined ? {} : { sourceConditions: resource.conditions }),
		    }));
		}
		function buildFunctionRows(payload) {
		    return payload.functions.map((componentFunction) => {
		        const denormalized = (0, component_function_denormalized_1.convertIComponentFunctionToComponentFunctionDenormalized)(componentFunction, payload.signals);
		        return {
		            name: denormalized.name,
		            functionClass: denormalized.functionClass ?? '',
		            busRole: denormalized.busRole ?? '',
		            mustBeAssigned: denormalized.mustBeAssigned,
		            resourceConsumed: denormalized.resourceConsumed ?? '',
		            functionSignals: denormalized.functionSignals,
		        };
		    });
		}
		function buildFunctionSignalMappingRows(payload) {
		    return payload.functions.flatMap((componentFunction) => {
		        const denormalized = (0, component_function_denormalized_1.convertIComponentFunctionToComponentFunctionDenormalized)(componentFunction, payload.signals);
		        return denormalized.functionSignals
		            .filter((functionSignal) => functionSignal.signalNames.length > 0)
		            .map((functionSignal) => ({
		            functionName: denormalized.name,
		            functionSignalName: functionSignal.name,
		            signalNames: functionSignal.signalNames,
		        }));
		    });
		}
		function anyArrayPayload(payloads) {
		    return payloads.some((payload) => (0, slice_contracts_1.readElementSigmaModelType)(payload) !== '');
		}
		class GeneralSigmaModelTypeSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = slice_contracts_1.SIGMA_MODEL_TYPE_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new scalar_comparison_1.ScalarComparison(slice_contracts_1.SIGMA_MODEL_TYPE_SLICE_KEY);
		    }
		    appliesTo() {
		        return true;
		    }
		    buildData(payload) {
		        return payload.sigmaModelType;
		    }
		}
		class GeneralElementSigmaModelTypeSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = slice_contracts_1.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ModelType;
		        this.spec = new scalar_comparison_1.ScalarComparison(slice_contracts_1.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY);
		    }
		    appliesTo(payloads) {
		        return anyArrayPayload(payloads);
		    }
		    buildData(payload) {
		        return (0, slice_contracts_1.readElementSigmaModelType)(payload);
		    }
		}
		class SignalSetSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.SIGNAL_SET_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new signal_set_comparison_1.SignalSetComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.signals.length > 0);
		    }
		    buildData(payload) {
		        return buildSignalSetRows(payload);
		    }
		}
		class ModesSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.MODES_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new modes_comparison_1.ModesComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.modes.length > 0);
		    }
		    buildData(payload, logger) {
		        return buildModeRows(payload, logger);
		    }
		}
		class SignalAttributesSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.SIGNAL_ATTRIBUTES_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new signal_attributes_comparison_1.SignalAttributesComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.signals.length > 0);
		    }
		    buildData(payload) {
		        return buildSignalAttributeRows(payload);
		    }
		}
		class PinoutSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.PINOUT_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new pinout_comparison_1.PinoutComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => Object.keys(payload.pinout).length > 0);
		    }
		    buildData(payload) {
		        return buildPinoutRows(payload);
		    }
		}
		class PinAssignmentsSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.PIN_ASSIGNMENTS_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new pin_assignments_comparison_1.PinAssignmentsComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => Object.keys(payload.pinout).length > 0);
		    }
		    buildData(payload) {
		        return buildPinAssignmentRows(payload);
		    }
		}
		class FloatingBehaviourSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.FLOATING_BEHAVIOUR_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new floating_behaviour_comparison_1.FloatingBehaviourComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.signals.some((signal) => signal.floatingBehavior !== undefined));
		    }
		    buildData(payload) {
		        return buildFloatingBehaviourRows(payload);
		    }
		}
		class SignalTiesSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.SIGNAL_TIES_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new signal_ties_comparison_1.SignalTiesComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.signals.some((signal) => (signal.ties ?? []).length > 0));
		    }
		    buildData(payload, logger) {
		        return buildSignalTieRows(payload, logger);
		    }
		}
		class SilentLogger {
		    info() { }
		    warn() { }
		    error() { }
		}
		const APPLICABILITY_PROBE_LOGGER = new SilentLogger();
		class BoundaryConditionsSlice extends GeneralReviewSlice {
		    constructor(sliceKey, kind) {
		        super();
		        this.sliceKey = sliceKey;
		        this.kind = kind;
		        this.spec = new boundary_conditions_comparison_1.BoundaryConditionsComparison();
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => this.buildData(payload, APPLICABILITY_PROBE_LOGGER).length > 0);
		    }
		    buildData(payload, logger) {
		        return buildBoundaryConditionRows(payload, this.kind, logger);
		    }
		}
		class DriveModesSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.DRIVE_MODES_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new drive_modes_comparison_1.DriveModesComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.signals.some((signal) => (signal.supportedDriveModes ?? []).length > 0));
		    }
		    buildData(payload, logger) {
		        return buildDriveModeRows(payload, logger);
		    }
		}
		class ResourcesSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.RESOURCES_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new resources_comparison_1.ResourcesComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.resources.length > 0);
		    }
		    buildData(payload, logger) {
		        return buildResourceRows(payload, logger);
		    }
		}
		class FunctionsSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.FUNCTIONS_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new functions_comparison_1.FunctionsComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.functions.length > 0);
		    }
		    buildData(payload) {
		        return buildFunctionRows(payload);
		    }
		}
		class FunctionSignalMappingSlice extends GeneralReviewSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.FUNCTION_SIGNAL_MAPPING_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new function_signal_mapping_comparison_1.FunctionSignalMappingComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => buildFunctionSignalMappingRows(payload).length > 0);
		    }
		    buildData(payload) {
		        return buildFunctionSignalMappingRows(payload);
		    }
		}
		class GeneralParametersSlice extends GeneralReviewSlice {
		    constructor(paramUnitsResolver, sigmaModelType) {
		        super();
		        this.paramUnitsResolver = paramUnitsResolver;
		        this.sliceKey = slice_contracts_1.PARAMETERS_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ModelType;
		        this.spec = new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
		        this.stampedSpec = this.specFor(sigmaModelType);
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => Object.keys(payload.parameters).length > 0);
		    }
		    buildData(payload) {
		        return payload.parameters;
		    }
		    resolveSpec(payloads) {
		        const modelType = payloads[0]?.sigmaModelType;
		        return modelType === undefined ? this.stampedSpec : this.specFor(modelType);
		    }
		    specFor(sigmaModelType) {
		        if (this.paramUnitsResolver === undefined || sigmaModelType === undefined || sigmaModelType.length === 0) {
		            return new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
		        }
		        return new sigma_model_parameters_comparison_1.SigmaModelParametersComparison({}, this.paramUnitsResolver(sigmaModelType));
		    }
		}
		function createGeneralReviewSlices(paramUnitsResolver, options) {
		    return [
		        new GeneralSigmaModelTypeSlice(),
		        new GeneralElementSigmaModelTypeSlice(),
		        new SignalSetSlice(),
		        ...(options?.includeModesSlice === true ? [new ModesSlice()] : []),
		        new SignalAttributesSlice(),
		        new PinoutSlice(),
		        new FloatingBehaviourSlice(),
		        new SignalTiesSlice(),
		        new BoundaryConditionsSlice(exports.AMR_BOUNDARIES_SLICE_KEY, BoundaryConditionKind.Amr),
		        new BoundaryConditionsSlice(exports.OC_BOUNDARIES_SLICE_KEY, BoundaryConditionKind.Oc),
		        new DriveModesSlice(),
		        new ResourcesSlice(),
		        new FunctionsSlice(),
		        new FunctionSignalMappingSlice(),
		        new GeneralParametersSlice(paramUnitsResolver, options?.sigmaModelType),
		        ...(options?.includePinAssignmentsSlice === true ? [new PinAssignmentsSlice()] : []),
		    ];
		}
		
	} (generalPackageSlices));
	return generalPackageSlices;
}

var hasRequiredSigmaModelFormSlices;

function requireSigmaModelFormSlices () {
	if (hasRequiredSigmaModelFormSlices) return sigmaModelFormSlices;
	hasRequiredSigmaModelFormSlices = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.VALUE_SLICE_KEYS = exports.NC_PINS_SLICE_KEY = exports.SIGNAL_MAPPING_SLICE_KEY = exports.STRUCTURE_SLICE_KEY = void 0;
		exports.buildFormStructureInstances = buildFormStructureInstances;
		exports.buildFormSignalMappingRows = buildFormSignalMappingRows;
		exports.buildFormStateFromSlices = buildFormStateFromSlices;
		exports.createSigmaModelFormSlices = createSigmaModelFormSlices;
		const sigma_engine_shared_1 = requireDist$1();
		const array_submodels_1 = requireArraySubmodels();
		const array_resolution_adapter_1 = requireArrayResolutionAdapter();
		const form_latest_1 = requireFormLatest$2();
		const no_connect_signal_1 = requireNoConnectSignal();
		const form_model_shape_1 = requireFormModelShape();
		const comparison_spec_1 = requireComparisonSpec();
		const boundary_conditions_comparison_1 = requireBoundaryConditions_comparison();
		const form_signal_mapping_comparison_1 = requireFormSignalMapping_comparison();
		const nc_pins_comparison_1 = requireNcPins_comparison();
		const form_structure_comparison_1 = requireFormStructure_comparison();
		const scalar_comparison_1 = requireScalar_comparison();
		const sigma_model_parameters_comparison_1 = requireSigmaModelParameters_comparison();
		const consensus_assembler_1 = requireConsensusAssembler();
		const general_package_slices_1 = requireGeneralPackageSlices();
		const multi_reviewer_consensus_service_1 = requireMultiReviewerConsensus_service();
		const slice_key_1 = requireSliceKey();
		const slice_scoped_logger_1 = requireSliceScopedLogger();
		const slice_contracts_1 = requireSliceContracts();
		exports.STRUCTURE_SLICE_KEY = slice_key_1.SliceKey.Structure;
		exports.SIGNAL_MAPPING_SLICE_KEY = slice_key_1.SliceKey.SignalMapping;
		exports.NC_PINS_SLICE_KEY = slice_key_1.SliceKey.NcPins;
		const formSliceLogger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)();
		class SigmaModelFormSlice {
		    resolveSpec(_payloads) {
		        return this.spec;
		    }
		    run(submissions, policies, logger) {
		        const service = new multi_reviewer_consensus_service_1.MultiReviewerConsensusService(new slice_scoped_logger_1.SliceScopedLogger(this.sliceKey, logger ?? formSliceLogger));
		        const projected = submissions.map(([reviewerId, payload]) => [reviewerId, this.buildData(payload)]);
		        const spec = this.resolveSpec(submissions.map(([, payload]) => payload));
		        return service.run(projected, {
		            spec,
		            confidencePolicy: policies.confidencePolicy,
		            triagePolicy: policies.triagePolicy,
		            assembler: new consensus_assembler_1.ConsensusAssembler(),
		        });
		    }
		    autoResolveColumnNames() {
		        return [...this.spec.autoResolveColumns.keys()].map(String);
		    }
		    keyColumnNames() {
		        const alignment = this.spec.alignment;
		        return alignment instanceof comparison_spec_1.CellAlignment ? alignment.keyColumns.map(String) : [];
		    }
		}
		function anyArrayPayload(payloads) {
		    return payloads.some((payload) => (0, slice_contracts_1.firstSigmaSubModel)(payload) !== undefined);
		}
		function parseSubModelRef(subModelId) {
		    const ref = (0, array_submodels_1.parseSubModelId)(subModelId);
		    if (ref === null) {
		        return { prefix: subModelId, index: 0 };
		    }
		    return { prefix: ref.prefix, index: ref.ordinal - 1 };
		}
		function repeatableBlockByModelSignal(payload, getFormDefinition) {
		    const definition = getFormDefinition(payload.sigmaModelType);
		    return new Map((definition?.signals ?? []).map((signal) => [String(signal.id), signal.repeatableBlock]));
		}
		function blockRefOfSignal(signal, blockByModelSignal) {
		    const name = signal.blockInfo?.name ??
		        (signal.modelSignal === undefined || signal.modelSignal === null ?
		            null
		            : (blockByModelSignal.get(String(signal.modelSignal)) ?? null));
		    return (0, form_latest_1.blockRefFor)(name, signal.blockInfo?.index ?? 0);
		}
		function buildFormStructureInstances(payload, getFormDefinition) {
		    const blockByModelSignal = repeatableBlockByModelSignal(payload, getFormDefinition);
		    const instances = [];
		    const seen = new Set();
		    const push = (instance) => {
		        const key = `${instance.kind}:${instance.blockName}:${instance.index}`;
		        if (!seen.has(key)) {
		            seen.add(key);
		            instances.push(instance);
		        }
		    };
		    const signals = (0, slice_contracts_1.firstSigmaSubModel)(payload) === undefined ? payload.signals : [];
		    for (const signal of signals) {
		        const ref = blockRefOfSignal(signal, blockByModelSignal);
		        if (ref === null) {
		            continue;
		        }
		        push({
		            kind: form_structure_comparison_1.FormStructureInstanceKind.BlockInstance,
		            blockName: ref.name,
		            index: ref.index,
		        });
		    }
		    for (const submodel of payload.spiceModels.filter(sigma_engine_shared_1.SPICE.isSubModelSigmaSubModel)) {
		        const { prefix, index } = parseSubModelRef(submodel.subModelId);
		        push({ kind: form_structure_comparison_1.FormStructureInstanceKind.ArrayElement, blockName: prefix, index });
		    }
		    return instances;
		}
		function buildFormSignalMappingRows(payload, getFormDefinition) {
		    const sigmaSubModels = payload.spiceModels.filter(sigma_engine_shared_1.SPICE.isSubModelSigmaSubModel);
		    if (sigmaSubModels.length === 0) {
		        const blockByModelSignal = repeatableBlockByModelSignal(payload, getFormDefinition);
		        const modelSignalById = modelSignalsById(getFormDefinition(payload.sigmaModelType)?.signals);
		        return payload.signals
		            .filter((signal) => !(0, no_connect_signal_1.isNoConnectSignal)(signal))
		            .map((signal) => {
		            const ref = blockRefOfSignal(signal, blockByModelSignal);
		            const modelSignalRef = String(signal.modelSignal ?? signal.id);
		            return {
		                modelSignalRef,
		                blockName: ref?.name ?? '',
		                blockIndex: ref?.index ?? -1,
		                signalName: signal.name,
		                pins: [...(payload.pinout[String(signal.id)] ?? [])],
		                ...operatorSetFloatingBehavior(modelSignalById.get(modelSignalRef), signal.floatingBehavior),
		            };
		        });
		    }
		    const signalById = new Map(payload.signals.map((signal) => [String(signal.id), signal]));
		    const rows = [];
		    const connectedSignalIds = new Set();
		    for (const submodel of sigmaSubModels) {
		        const { prefix, index } = parseSubModelRef(submodel.subModelId);
		        const terminalById = modelSignalsById(getFormDefinition(submodel.modelId)?.signals);
		        for (const connection of submodel.modelInfo.signalConnectionMap) {
		            const componentSignalId = String(connection.componentSignalId);
		            const modelSignalRef = String(connection.modelSignalId);
		            const signal = signalById.get(componentSignalId);
		            connectedSignalIds.add(componentSignalId);
		            rows.push({
		                modelSignalRef,
		                blockName: prefix,
		                blockIndex: index,
		                signalName: signal?.name ?? '',
		                pins: [...(payload.pinout[componentSignalId] ?? [])],
		                ...operatorSetFloatingBehavior(terminalById.get(modelSignalRef), signal?.floatingBehavior),
		            });
		        }
		    }
		    for (const signal of payload.signals) {
		        const signalId = String(signal.id);
		        if ((0, no_connect_signal_1.isNoConnectSignal)(signal) || connectedSignalIds.has(signalId)) {
		            continue;
		        }
		        rows.push({
		            modelSignalRef: signalId,
		            blockName: signal.blockInfo?.name ?? '',
		            blockIndex: signal.blockInfo?.index ?? -1,
		            signalName: signal.name,
		            pins: [...(payload.pinout[signalId] ?? [])],
		            ...operatorSetFloatingBehavior(undefined, signal.floatingBehavior),
		        });
		    }
		    return rows;
		}
		function modelSignalsById(signals) {
		    return new Map((signals ?? []).map((signal) => [String(signal.id), signal]));
		}
		function operatorSetFloatingBehavior(modelSignal, floatingBehavior) {
		    return (0, form_model_shape_1.floatingBehaviorField)((0, form_model_shape_1.isFloatingBehaviorOperatorSet)(modelSignal) ? floatingBehavior : ((0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, floatingBehavior)));
		}
		function buildFormStateFromSlices(args) {
		    const isArray = args.elementSigmaModelType !== '';
		    const definition = args.getFormDefinition(isArray ? args.elementSigmaModelType : args.parentSigmaModelType);
		    const modelSignalById = new Map((definition?.signals ?? []).map((signal) => [String(signal.id), signal]));
		    const pinAssignments = args.mappingRows.map((row) => {
		        const modelSignal = modelSignalById.get(row.modelSignalRef);
		        const isUnbound = definition !== null && modelSignal === undefined;
		        const blockName = isArray || isUnbound ? row.blockName : (modelSignal?.repeatableBlock ?? null);
		        return {
		            modelSignalId: isUnbound ? form_latest_1.UNBOUND_MODEL_SIGNAL_ID : row.modelSignalRef,
		            block: (0, form_latest_1.blockRefFor)(blockName, row.blockIndex),
		            nameOverride: row.signalName === '' ? null : row.signalName,
		            pins: [...row.pins],
		            ...(isUnbound ? { packageSignalId: row.modelSignalRef } : {}),
		            ...(row.floatingBehavior !== undefined ? { floatingBehavior: row.floatingBehavior } : {}),
		        };
		    });
		    return {
		        formVersion: form_latest_1.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
		        parentSigmaModelType: args.parentSigmaModelType,
		        elementSigmaModelType: isArray ? args.elementSigmaModelType : null,
		        pinCount: null,
		        arrayConfirmed: isArray ? true : null,
		        pinAssignments,
		        ncPins: [...args.nc.pins],
		        ...(args.nc.floatingBehavior !== undefined ? { ncFloatingBehavior: args.nc.floatingBehavior } : {}),
		        parameters: args.parameters,
		    };
		}
		class SigmaModelTypeSlice extends SigmaModelFormSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = slice_contracts_1.SIGMA_MODEL_TYPE_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new scalar_comparison_1.ScalarComparison(slice_contracts_1.SIGMA_MODEL_TYPE_SLICE_KEY);
		    }
		    appliesTo() {
		        return true;
		    }
		    buildData(payload) {
		        return payload.sigmaModelType;
		    }
		}
		class ElementSigmaModelTypeSlice extends SigmaModelFormSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = slice_contracts_1.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ModelType;
		        this.spec = new scalar_comparison_1.ScalarComparison(slice_contracts_1.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY);
		    }
		    appliesTo(payloads) {
		        return anyArrayPayload(payloads);
		    }
		    buildData(payload) {
		        return (0, slice_contracts_1.readElementSigmaModelType)(payload);
		    }
		}
		class StructureSlice extends SigmaModelFormSlice {
		    constructor(getFormDefinition) {
		        super();
		        this.getFormDefinition = getFormDefinition;
		        this.sliceKey = exports.STRUCTURE_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ElementType;
		        this.spec = new form_structure_comparison_1.FormStructureComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => buildFormStructureInstances(payload, this.getFormDefinition).length > 0);
		    }
		    buildData(payload) {
		        return buildFormStructureInstances(payload, this.getFormDefinition);
		    }
		}
		class SignalMappingSlice extends SigmaModelFormSlice {
		    constructor(getFormDefinition) {
		        super();
		        this.getFormDefinition = getFormDefinition;
		        this.sliceKey = exports.SIGNAL_MAPPING_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ElementType;
		        this.spec = new form_signal_mapping_comparison_1.FormSignalMappingComparison();
		    }
		    appliesTo() {
		        return true;
		    }
		    buildData(payload) {
		        return buildFormSignalMappingRows(payload, this.getFormDefinition);
		    }
		}
		class NcPinsSlice extends SigmaModelFormSlice {
		    constructor() {
		        super(...arguments);
		        this.sliceKey = exports.NC_PINS_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		        this.spec = new nc_pins_comparison_1.NcPinsComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => payload.signals.some(no_connect_signal_1.isNoConnectSignal));
		    }
		    buildData(payload) {
		        const floatingBehavior = payload.signals.find(no_connect_signal_1.isNoConnectSignal)?.floatingBehavior;
		        return {
		            pins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
		            ...(floatingBehavior !== undefined ? { floatingBehavior } : {}),
		        };
		    }
		}
		function resolveParametersSpec(resolver, modelType) {
		    if (resolver === undefined || modelType === undefined || modelType.length === 0) {
		        return new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
		    }
		    return new sigma_model_parameters_comparison_1.SigmaModelParametersComparison({}, resolver(modelType));
		}
		class ParametersSlice extends SigmaModelFormSlice {
		    constructor(paramUnitsResolver) {
		        super();
		        this.paramUnitsResolver = paramUnitsResolver;
		        this.sliceKey = slice_contracts_1.PARAMETERS_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ModelType;
		        this.spec = new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => Object.keys(payload.parameters).length > 0);
		    }
		    buildData(payload) {
		        return payload.parameters;
		    }
		    resolveSpec(payloads) {
		        return resolveParametersSpec(this.paramUnitsResolver, payloads[0]?.sigmaModelType);
		    }
		}
		class ElementParametersSlice extends SigmaModelFormSlice {
		    constructor(paramUnitsResolver) {
		        super();
		        this.paramUnitsResolver = paramUnitsResolver;
		        this.sliceKey = slice_contracts_1.ELEMENT_PARAMETERS_SLICE_KEY;
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ElementType;
		        this.spec = new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
		    }
		    appliesTo(payloads) {
		        return anyArrayPayload(payloads);
		    }
		    buildData(payload) {
		        return (0, array_resolution_adapter_1.readArrayElementParametersFromSubModels)(payload.spiceModels);
		    }
		    resolveSpec(payloads) {
		        const elementType = payloads[0] === undefined ? undefined : (0, slice_contracts_1.readElementSigmaModelType)(payloads[0]);
		        return resolveParametersSpec(this.paramUnitsResolver, elementType);
		    }
		}
		class SilentFormSliceLogger {
		    info() { }
		    warn() { }
		    error() { }
		}
		const APPLICABILITY_PROBE_LOGGER = new SilentFormSliceLogger();
		class FormBoundaryConditionsSlice extends SigmaModelFormSlice {
		    constructor(sliceKey, kind) {
		        super();
		        this.sliceKey = sliceKey;
		        this.kind = kind;
		        this.spec = new boundary_conditions_comparison_1.BoundaryConditionsComparison();
		        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
		    }
		    appliesTo(payloads) {
		        return payloads.some((payload) => this.buildData(payload, APPLICABILITY_PROBE_LOGGER).length > 0);
		    }
		    buildData(payload, logger = formSliceLogger) {
		        return (0, general_package_slices_1.buildBoundaryConditionRows)({ boundaries: payload.boundaries ?? [], signals: payload.signals, modes: [] }, this.kind, logger);
		    }
		}
		function createSigmaModelFormSlices(paramUnitsResolver, definitionResolver = () => null) {
		    return [
		        new SigmaModelTypeSlice(),
		        new ElementSigmaModelTypeSlice(),
		        new StructureSlice(definitionResolver),
		        new SignalMappingSlice(definitionResolver),
		        new NcPinsSlice(),
		        new ParametersSlice(paramUnitsResolver),
		        new ElementParametersSlice(paramUnitsResolver),
		        new FormBoundaryConditionsSlice(general_package_slices_1.AMR_BOUNDARIES_SLICE_KEY, general_package_slices_1.BoundaryConditionKind.Amr),
		        new FormBoundaryConditionsSlice(general_package_slices_1.OC_BOUNDARIES_SLICE_KEY, general_package_slices_1.BoundaryConditionKind.Oc),
		    ];
		}
		exports.VALUE_SLICE_KEYS = [
		    exports.STRUCTURE_SLICE_KEY,
		    exports.SIGNAL_MAPPING_SLICE_KEY,
		    exports.NC_PINS_SLICE_KEY,
		    slice_contracts_1.PARAMETERS_SLICE_KEY,
		    slice_contracts_1.ELEMENT_PARAMETERS_SLICE_KEY,
		    general_package_slices_1.AMR_BOUNDARIES_SLICE_KEY,
		    general_package_slices_1.OC_BOUNDARIES_SLICE_KEY,
		];
		
	} (sigmaModelFormSlices));
	return sigmaModelFormSlices;
}

var reviewSubmissionPayload = {};

var hasRequiredReviewSubmissionPayload;

function requireReviewSubmissionPayload () {
	if (hasRequiredReviewSubmissionPayload) return reviewSubmissionPayload;
	hasRequiredReviewSubmissionPayload = 1;
	Object.defineProperty(reviewSubmissionPayload, "__esModule", { value: true });
	reviewSubmissionPayload.ReviewSubmissionPayloadSchema = void 0;
	reviewSubmissionPayload.formPayloadToReviewSubmissionPayload = formPayloadToReviewSubmissionPayload;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const misc_1 = requireMisc();
	reviewSubmissionPayload.ReviewSubmissionPayloadSchema = z
	    .object({
	    sigmaModelType: z.string(),
	    signals: sigma_engine_shared_1.SignalSchema.array(),
	    parameters: misc_1.pkgParametersSchema,
	    pinout: misc_1.pkgPinoutSchema,
	    boundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
	    resources: sigma_engine_shared_1.IComponentResourceSchema.array(),
	    functions: sigma_engine_shared_1.IComponentFunctionSchema.array(),
	    modes: sigma_engine_shared_1.ModeBaseSchema.array(),
	    spiceModels: sigma_engine_shared_1.SPICE.ISubModelOptsSchema.array(),
	})
	    .superRefine((payload, ctx) => {
	    const seen = new Set();
	    payload.signals.forEach((signal, index) => {
	        const nameKey = (0, signal_name_normalization_1.normalizeSignalNameForComparison)(signal.name);
	        if (seen.has(nameKey)) {
	            ctx.addIssue({
	                code: z.ZodIssueCode.custom,
	                message: `Duplicate signal name: "${signal.name}"`,
	                path: ['signals', index, 'name'],
	            });
	            return;
	        }
	        seen.add(nameKey);
	    });
	});
	function formPayloadToReviewSubmissionPayload(form) {
	    return {
	        sigmaModelType: form.sigmaModelType,
	        signals: form.signals,
	        parameters: form.parameters,
	        pinout: form.pinout,
	        boundaries: form.boundaries ?? [],
	        resources: [],
	        functions: [],
	        modes: [],
	        spiceModels: form.spiceModels,
	    };
	}
	
	return reviewSubmissionPayload;
}

var resolutionProjection = {};

var hasRequiredResolutionProjection;

function requireResolutionProjection () {
	if (hasRequiredResolutionProjection) return resolutionProjection;
	hasRequiredResolutionProjection = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PROJECTED_RESOLUTION_REVIEWER_ID = void 0;
		exports.readPinout = readPinout;
		exports.readParameters = readParameters;
		exports.resolutionToGeneralPayload = resolutionToGeneralPayload;
		exports.extractResolutionAnchors = extractResolutionAnchors;
		exports.projectResolutionToSliceTables = projectResolutionToSliceTables;
		const consensus_types_1 = requireConsensus_types();
		const confidence_policy_1 = requireConfidencePolicy();
		const triage_policy_1 = requireTriagePolicy();
		exports.PROJECTED_RESOLUTION_REVIEWER_ID = '__ground_truth__';
		function isRowRecord(value) {
		    return typeof value === 'object' && value !== null && !Array.isArray(value);
		}
		function readPinout(pins) {
		    const pinout = {};
		    for (const pin of pins) {
		        if (pin.signalRef === null || pin.signalRef === undefined) {
		            continue;
		        }
		        pinout[pin.signalRef] ??= [];
		        pinout[pin.signalRef].push(pin.name);
		    }
		    return pinout;
		}
		function readParameters(sigmaModelParameters) {
		    const parameters = {};
		    for (const [key, valueVariance] of Object.entries(sigmaModelParameters)) {
		        parameters[key] = {
		            value: valueVariance.value === undefined ? null
		                : typeof valueVariance.value === 'number' ? String(valueVariance.value)
		                    : valueVariance.value,
		            variance: valueVariance.variance ?? null,
		        };
		    }
		    return parameters;
		}
		function resolutionToGeneralPayload(sigmaModelType, resolution) {
		    return {
		        sigmaModelType,
		        signals: resolution.signals ?? [],
		        parameters: readParameters(resolution.sigmaModelParameters ?? {}),
		        pinout: readPinout(resolution.pins ?? []),
		        boundaries: resolution.boundaries ?? [],
		        resources: resolution.resources ?? [],
		        functions: resolution.functions ?? [],
		        modes: resolution.modes ?? [],
		        spiceModels: resolution.spiceModels ?? [],
		    };
		}
		const PROJECTION_POLICIES = {
		    confidencePolicy: new confidence_policy_1.ProportionalConfidencePolicy({ minReviewers: 1 }),
		    triagePolicy: new triage_policy_1.ThresholdTriagePolicy(1),
		};
		function extractResolutionAnchors(slices, payload, logger) {
		    const bySlice = new Map();
		    for (const slice of slices) {
		        if (!slice.appliesTo([payload])) {
		            continue;
		        }
		        const assembly = slice.run([[exports.PROJECTED_RESOLUTION_REVIEWER_ID, payload]], PROJECTION_POLICIES, logger);
		        const rowKeys = new Set();
		        const rowContentByRowKey = new Map();
		        const fieldNormalizedByAnchorKey = new Map();
		        const fieldRawByAnchorKey = new Map();
		        const fieldRowKeyByAnchorKey = new Map();
		        const fieldColumnByAnchorKey = new Map();
		        for (const tally of assembly.annotations) {
		            if (tally.anchor.kind === consensus_types_1.AnchorKind.Row) {
		                rowKeys.add(tally.anchor.rowKey);
		                const representative = tally.candidates[0]?.value;
		                if (isRowRecord(representative)) {
		                    rowContentByRowKey.set(tally.anchor.rowKey, representative);
		                }
		                continue;
		            }
		            const candidate = tally.candidates[0];
		            if (candidate === undefined) {
		                continue;
		            }
		            const key = (0, consensus_types_1.anchorKey)(tally.anchor);
		            fieldNormalizedByAnchorKey.set(key, candidate.normalizedValue);
		            fieldRawByAnchorKey.set(key, candidate.value);
		            fieldRowKeyByAnchorKey.set(key, tally.anchor.rowKey);
		            fieldColumnByAnchorKey.set(key, tally.anchor.column);
		        }
		        bySlice.set(slice.sliceKey, {
		            sliceKey: slice.sliceKey,
		            rowKeys,
		            rowContentByRowKey,
		            fieldNormalizedByAnchorKey,
		            fieldRawByAnchorKey,
		            fieldRowKeyByAnchorKey,
		            fieldColumnByAnchorKey,
		        });
		    }
		    return bySlice;
		}
		function projectResolutionToSliceTables(slices, payload, logger) {
		    const bySlice = new Map();
		    for (const slice of slices) {
		        if (!slice.appliesTo([payload])) {
		            continue;
		        }
		        const assembly = slice.run([[exports.PROJECTED_RESOLUTION_REVIEWER_ID, payload]], PROJECTION_POLICIES, logger);
		        bySlice.set(slice.sliceKey, assembly.table);
		    }
		    return bySlice;
		}
		
	} (resolutionProjection));
	return resolutionProjection;
}

var applySignalConsolidationMap = {};

var hasRequiredApplySignalConsolidationMap;

function requireApplySignalConsolidationMap () {
	if (hasRequiredApplySignalConsolidationMap) return applySignalConsolidationMap;
	hasRequiredApplySignalConsolidationMap = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SignalConsolidationMapSchema = exports.SignalConsolidationEntrySchema = void 0;
		exports.applySignalConsolidation = applySignalConsolidation;
		const z = /*@__PURE__*/ requireZod();
		const signal_set_comparison_1 = requireSignalSet_comparison();
		exports.SignalConsolidationEntrySchema = z.object({
		    sourceKey: z.string(),
		    canonicalName: z.string().nullable(),
		});
		exports.SignalConsolidationMapSchema = exports.SignalConsolidationEntrySchema.array();
		function isRelativeSignalRef(value) {
		    return (value !== null && typeof value === 'object' && ('signal' in value || 'signal_name' in value));
		}
		function transformRelativeSignalRef(ref, droppedIds, droppedKeys, renameByKey) {
		    const ids = ref.signal === undefined ? []
		        : Array.isArray(ref.signal) ? ref.signal
		            : [ref.signal];
		    const keptIds = ids.filter((id) => !droppedIds.has(String(id)));
		    const names = ref.signal_name === undefined ? []
		        : Array.isArray(ref.signal_name) ? ref.signal_name
		            : [ref.signal_name];
		    const keptNames = names
		        .filter((name) => !droppedKeys.has((0, signal_set_comparison_1.signalSetRowKeyForName)(name)))
		        .map((name) => renameByKey.get((0, signal_set_comparison_1.signalSetRowKeyForName)(name)) ?? name);
		    if (ids.length + names.length > 0 && keptIds.length + keptNames.length === 0) {
		        return null;
		    }
		    const next = {};
		    if (ref.signal !== undefined && keptIds.length > 0) {
		        next.signal = Array.isArray(ref.signal) ? keptIds : keptIds[0];
		    }
		    if (ref.signal_name !== undefined && keptNames.length > 0) {
		        next.signal_name = Array.isArray(ref.signal_name) ? keptNames : keptNames[0];
		    }
		    return next;
		}
		function transformBoundaryDefinition(definition, droppedIds, droppedKeys, renameByKey) {
		    const next = { ...definition };
		    const writable = next;
		    for (const field of ['target', 'relative_to']) {
		        const value = next[field];
		        if (!isRelativeSignalRef(value)) {
		            continue;
		        }
		        const transformed = transformRelativeSignalRef(value, droppedIds, droppedKeys, renameByKey);
		        if (transformed === null) {
		            return null;
		        }
		        writable[field] = transformed;
		    }
		    return next;
		}
		function applySignalConsolidation(payload, map) {
		    const entryByKey = new Map(map.map((entry) => [entry.sourceKey, entry]));
		    const renameByKey = new Map(map
		        .filter((entry) => entry.canonicalName !== null)
		        .map((entry) => [entry.sourceKey, entry.canonicalName]));
		    const droppedKeys = new Set(map.filter((entry) => entry.canonicalName === null).map((entry) => entry.sourceKey));
		    const droppedIds = new Set();
		    const renamed = [];
		    for (const signal of payload.signals) {
		        const key = (0, signal_set_comparison_1.signalSetRowKeyForName)(signal.name);
		        const entry = entryByKey.get(key);
		        if (entry === undefined) {
		            renamed.push(signal);
		        }
		        else if (entry.canonicalName === null) {
		            droppedIds.add(String(signal.id));
		        }
		        else if (entry.canonicalName === signal.name) {
		            renamed.push(signal);
		        }
		        else {
		            renamed.push({ ...signal, name: entry.canonicalName });
		        }
		    }
		    const seenNameKeys = new Set();
		    const duplicateNameIds = [];
		    const signals = [];
		    for (const signal of renamed) {
		        const nameKey = (0, signal_set_comparison_1.signalSetRowKeyForName)(signal.name);
		        if (seenNameKeys.has(nameKey)) {
		            droppedIds.add(String(signal.id));
		            duplicateNameIds.push(String(signal.id));
		            continue;
		        }
		        seenNameKeys.add(nameKey);
		        signals.push(signal);
		    }
		    let tieCount = 0;
		    const signalsWithTies = signals.map((signal) => {
		        const ties = signal.ties ?? [];
		        const keptTies = ties.filter((tie) => tie.rootComponentSignalId === undefined || !droppedIds.has(String(tie.rootComponentSignalId)));
		        tieCount += ties.length - keptTies.length;
		        return keptTies.length === ties.length ? signal : { ...signal, ties: keptTies };
		    });
		    const pinout = Object.fromEntries(Object.entries(payload.pinout).filter(([signalId]) => !droppedIds.has(signalId)));
		    const droppedBoundaryIds = [];
		    const boundaries = [];
		    for (const boundary of payload.boundaries) {
		        const definition = transformBoundaryDefinition(boundary.boundary, droppedIds, droppedKeys, renameByKey);
		        if (definition === null) {
		            droppedBoundaryIds.push(boundary.id);
		            continue;
		        }
		        boundaries.push(definition === boundary.boundary ? boundary : { ...boundary, boundary: definition });
		    }
		    let functionRefCount = 0;
		    const functions = payload.functions.map((componentFunction) => ({
		        ...componentFunction,
		        functionSignals: componentFunction.functionSignals.map((functionSignal) => {
		            const kept = functionSignal.signals.flatMap((ref) => {
		                const transformed = transformRelativeSignalRef(ref, droppedIds, droppedKeys, renameByKey);
		                if (transformed === null) {
		                    functionRefCount += 1;
		                    return [];
		                }
		                return [transformed];
		            });
		            return { ...functionSignal, signals: kept };
		        }),
		    }));
		    return {
		        payload: {
		            ...payload,
		            signals: signalsWithTies,
		            pinout,
		            boundaries,
		            functions,
		        },
		        dropped: {
		            signalIds: [...droppedIds],
		            boundaryIds: droppedBoundaryIds,
		            tieCount,
		            functionRefCount,
		            duplicateNameIds,
		        },
		    };
		}
		
	} (applySignalConsolidationMap));
	return applySignalConsolidationMap;
}

var applyModeConsolidationMap = {};

var hasRequiredApplyModeConsolidationMap;

function requireApplyModeConsolidationMap () {
	if (hasRequiredApplyModeConsolidationMap) return applyModeConsolidationMap;
	hasRequiredApplyModeConsolidationMap = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ModeConsolidationMapSchema = exports.ModeConsolidationEntrySchema = void 0;
		exports.applyModeConsolidation = applyModeConsolidation;
		const z = /*@__PURE__*/ requireZod();
		const general_package_slices_1 = requireGeneralPackageSlices();
		exports.ModeConsolidationEntrySchema = z.object({
		    sourceKey: z.string(),
		    keep: z.boolean(),
		});
		exports.ModeConsolidationMapSchema = exports.ModeConsolidationEntrySchema.array();
		function applyModeConsolidation(payload, map, rowKeyOf) {
		    const discardedKeys = new Set(map.filter((entry) => !entry.keep).map((entry) => entry.sourceKey));
		    if (discardedKeys.size === 0) {
		        return { payload, droppedModeIds: [] };
		    }
		    const discardedModeIds = new Set((0, general_package_slices_1.buildModeRows)(payload)
		        .filter((row) => discardedKeys.has(rowKeyOf(row)))
		        .map((row) => row.id));
		    if (discardedModeIds.size === 0) {
		        return { payload, droppedModeIds: [] };
		    }
		    return {
		        payload: { ...payload, modes: payload.modes.filter((mode) => !discardedModeIds.has(mode.id)) },
		        droppedModeIds: [...discardedModeIds],
		    };
		}
		
	} (applyModeConsolidationMap));
	return applyModeConsolidationMap;
}

var matchLeniency = {};

var hasRequiredMatchLeniency;

function requireMatchLeniency () {
	if (hasRequiredMatchLeniency) return matchLeniency;
	hasRequiredMatchLeniency = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.MATCH_LENIENCY_LADDER = exports.MATCH_LENIENCY_RUNGS = exports.MatchLeniency = void 0;
		var MatchLeniency;
		(function (MatchLeniency) {
		    MatchLeniency["Strict"] = "strict";
		    MatchLeniency["FoldedSignalNotation"] = "folded_signal_notation";
		    MatchLeniency["IgnoredConditions"] = "ignored_conditions";
		    MatchLeniency["Unmatched"] = "unmatched";
		})(MatchLeniency || (exports.MatchLeniency = MatchLeniency = {}));
		exports.MATCH_LENIENCY_RUNGS = [
		    MatchLeniency.Strict,
		    MatchLeniency.FoldedSignalNotation,
		    MatchLeniency.IgnoredConditions,
		];
		exports.MATCH_LENIENCY_LADDER = [...exports.MATCH_LENIENCY_RUNGS, MatchLeniency.Unmatched];
		
	} (matchLeniency));
	return matchLeniency;
}

var keyReconciliation = {};

var hasRequiredKeyReconciliation;

function requireKeyReconciliation () {
	if (hasRequiredKeyReconciliation) return keyReconciliation;
	hasRequiredKeyReconciliation = 1;
	Object.defineProperty(keyReconciliation, "__esModule", { value: true });
	keyReconciliation.RECONCILABLE_RUNGS = keyReconciliation.strictFold = void 0;
	keyReconciliation.dropTrailingComponent = dropTrailingComponent;
	keyReconciliation.blankComponent = blankComponent;
	keyReconciliation.foldComponents = foldComponents;
	keyReconciliation.composeFolds = composeFolds;
	keyReconciliation.reconcileKeys = reconcileKeys;
	keyReconciliation.leniencyOf = leniencyOf;
	const value_comparison_1 = requireValueComparison();
	const match_leniency_1 = requireMatchLeniency();
	const strictFold = (rowKey) => rowKey;
	keyReconciliation.strictFold = strictFold;
	function dropTrailingComponent(componentCount) {
	    return (rowKey) => {
	        const parts = rowKey.split(value_comparison_1.COMPOSITE_KEY_SEPARATOR);
	        if (parts.length !== componentCount) {
	            return rowKey;
	        }
	        return parts.slice(0, -1).join(value_comparison_1.COMPOSITE_KEY_SEPARATOR);
	    };
	}
	function blankComponent(index, componentCount) {
	    return (rowKey) => {
	        const parts = rowKey.split(value_comparison_1.COMPOSITE_KEY_SEPARATOR);
	        if (parts.length !== componentCount || index < 0 || index >= parts.length) {
	            return rowKey;
	        }
	        parts[index] = '';
	        return parts.join(value_comparison_1.COMPOSITE_KEY_SEPARATOR);
	    };
	}
	function foldComponents(transform) {
	    return (rowKey) => rowKey.split(value_comparison_1.COMPOSITE_KEY_SEPARATOR).map(transform).join(value_comparison_1.COMPOSITE_KEY_SEPARATOR);
	}
	function composeFolds(...folds) {
	    return (rowKey) => folds.reduce((key, fold) => fold(key), rowKey);
	}
	function reconcileKeys(sourceKeys, targetKeys, rungs) {
	    const matches = new Map();
	    const remainingSource = [...new Set(sourceKeys)].sort();
	    const remainingTarget = new Set(targetKeys);
	    for (const rung of rungs) {
	        const stillUnmatched = [];
	        const targetsByFold = new Map();
	        for (const targetKey of [...remainingTarget].sort()) {
	            const folded = rung.fold(targetKey);
	            const bucket = targetsByFold.get(folded);
	            if (bucket === undefined) {
	                targetsByFold.set(folded, [targetKey]);
	            }
	            else {
	                bucket.push(targetKey);
	            }
	        }
	        for (const sourceKey of remainingSource) {
	            const bucket = targetsByFold.get(rung.fold(sourceKey));
	            const targetKey = bucket?.shift();
	            if (targetKey === undefined) {
	                stillUnmatched.push(sourceKey);
	                continue;
	            }
	            matches.set(sourceKey, { sourceKey, targetKey, leniency: rung.leniency });
	            remainingTarget.delete(targetKey);
	        }
	        remainingSource.length = 0;
	        remainingSource.push(...stillUnmatched);
	    }
	    return {
	        matches,
	        unmatchedSourceKeys: [...remainingSource],
	        unmatchedTargetKeys: [...remainingTarget].sort(),
	    };
	}
	function leniencyOf(reconciliation, sourceKey) {
	    return reconciliation.matches.get(sourceKey)?.leniency ?? match_leniency_1.MatchLeniency.Unmatched;
	}
	keyReconciliation.RECONCILABLE_RUNGS = match_leniency_1.MATCH_LENIENCY_RUNGS;
	
	return keyReconciliation;
}

var hasRequiredConsensus;

function requireConsensus () {
	if (hasRequiredConsensus) return Consensus;
	hasRequiredConsensus = 1;
	(function (exports) {
		var __createBinding = (Consensus && Consensus.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Consensus && Consensus.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireSliceKey(), exports);
		__exportStar(requirePropertyKey(), exports);
		__exportStar(requireSliceVersion(), exports);
		__exportStar(requireConsensus_types(), exports);
		__exportStar(requireComparisonSpec(), exports);
		__exportStar(requireConfidencePolicy(), exports);
		__exportStar(requireTriagePolicy(), exports);
		__exportStar(requireAutoResolvePolicy(), exports);
		__exportStar(requireConsensusAssembler(), exports);
		__exportStar(requireMultiReviewerConsensus_service(), exports);
		__exportStar(requireSliceScopedLogger(), exports);
		__exportStar(requireSignalAttributes_comparison(), exports);
		__exportStar(requireDriveModes_comparison(), exports);
		__exportStar(requireSigmaModelParameters_comparison(), exports);
		__exportStar(requirePinout_comparison(), exports);
		__exportStar(requirePinAssignments_comparison(), exports);
		__exportStar(requirePinIdNormalization(), exports);
		__exportStar(requireScalar_comparison(), exports);
		__exportStar(requireFormStructure_comparison(), exports);
		__exportStar(requireFormSignalMapping_comparison(), exports);
		__exportStar(requireFunctionSignalMapping_comparison(), exports);
		__exportStar(requireNcPins_comparison(), exports);
		__exportStar(requireSignalSet_comparison(), exports);
		__exportStar(requireBoundaryConditions_comparison(), exports);
		__exportStar(requireFloatingBehaviour_comparison(), exports);
		__exportStar(requireSignalTies_comparison(), exports);
		__exportStar(requireResources_comparison(), exports);
		__exportStar(requireFunctions_comparison(), exports);
		__exportStar(requireModes_comparison(), exports);
		__exportStar(requireSliceContracts(), exports);
		__exportStar(requireSigmaModelFormSlices(), exports);
		__exportStar(requireReviewSubmissionPayload(), exports);
		__exportStar(requireResolutionProjection(), exports);
		__exportStar(requireGeneralPackageSlices(), exports);
		__exportStar(requireConditionCanonicalization(), exports);
		__exportStar(requireApplySignalConsolidationMap(), exports);
		__exportStar(requireApplyModeConsolidationMap(), exports);
		__exportStar(requireMatchLeniency(), exports);
		__exportStar(requireKeyReconciliation(), exports);
		
	} (Consensus));
	return Consensus;
}

var DnpColumns = {};

var dnpClassificationGate = {};

var altiumProjectFileUtil = {};

var altiumFileTypes = {};

var hasRequiredAltiumFileTypes;

function requireAltiumFileTypes () {
	if (hasRequiredAltiumFileTypes) return altiumFileTypes;
	hasRequiredAltiumFileTypes = 1;
	Object.defineProperty(altiumFileTypes, "__esModule", { value: true });
	altiumFileTypes.AltiumFileType = void 0;
	var AltiumFileType;
	(function (AltiumFileType) {
	    AltiumFileType["PcbDoc"] = ".PcbDoc";
	    AltiumFileType["SchDoc"] = ".SchDoc";
	    AltiumFileType["Sch"] = ".Sch";
	    AltiumFileType["Project"] = ".PrjPCB";
	    AltiumFileType["ProjectStructure"] = ".PrjPcbStructure";
	    AltiumFileType["ProjectVariants"] = ".PrjPcbVariants";
	    AltiumFileType["OutJob"] = ".OutJob";
	    AltiumFileType["PcbDrawing"] = ".PCBDwf";
	    AltiumFileType["Harness"] = ".Harness";
	    AltiumFileType["BomDoc"] = ".BomDoc";
	    AltiumFileType["Stackup"] = ".stackup";
	    AltiumFileType["Netlist"] = ".Net";
	    AltiumFileType["Annotation"] = ".Annotation";
	})(AltiumFileType || (altiumFileTypes.AltiumFileType = AltiumFileType = {}));
	
	return altiumFileTypes;
}

var altiumValidation = {};

var projectDetection = {};

var hasRequiredProjectDetection;

function requireProjectDetection () {
	if (hasRequiredProjectDetection) return projectDetection;
	hasRequiredProjectDetection = 1;
	Object.defineProperty(projectDetection, "__esModule", { value: true });
	projectDetection.toForwardSlash = toForwardSlash;
	projectDetection.normalizePathForCompare = normalizePathForCompare;
	projectDetection.basenameWithoutExtension = basenameWithoutExtension;
	projectDetection.dirnameForward = dirnameForward;
	projectDetection.isSameOrDescendantDir = isSameOrDescendantDir;
	projectDetection.buildProjectDefinition = buildProjectDefinition;
	projectDetection.buildBlobMap = buildBlobMap;
	projectDetection.projectDisplayName = projectDisplayName;
	projectDetection.endsWithExt = endsWithExt;
	projectDetection.endsWithAnyExt = endsWithAnyExt;
	projectDetection.detectAnchoredProjects = detectAnchoredProjects;
	const sigma_engine_shared_1 = requireDist$1();
	function toForwardSlash(p) {
	    return p.replace(/\\/g, '/');
	}
	function normalizePathForCompare(p) {
	    return sigma_engine_shared_1.Files.normalizePath(toForwardSlash(p)).toLowerCase();
	}
	function basenameWithoutExtension(relativePath) {
	    const base = sigma_engine_shared_1.Files.basenamePath(toForwardSlash(relativePath));
	    const dot = base.lastIndexOf('.');
	    return dot > 0 ? base.substring(0, dot) : base;
	}
	function dirnameForward(relativePath) {
	    return sigma_engine_shared_1.Files.dirnamePath(toForwardSlash(relativePath));
	}
	function isSameOrDescendantDir(child, ancestor) {
	    const c = normalizePathForCompare(child);
	    const a = normalizePathForCompare(ancestor);
	    if (c === a) {
	        return true;
	    }
	    if (a === '' || a === '.') {
	        return true;
	    }
	    return c.startsWith(`${a}/`);
	}
	function buildProjectDefinition(type, name, fileDetails, blobMap) {
	    const otherFiles = {};
	    const definition = {
	        type,
	        name,
	        boards: {},
	        schematics: {},
	        other: otherFiles,
	    };
	    for (const detail of fileDetails) {
	        const isClaimedAuxiliaryFailure = detail.semanticType === 'other' &&
	            detail.fileValidationResult?.isValid === false &&
	            detail.fileValidationResult.claimedTools?.includes(type) === true;
	        if ((detail.severity === sigma_engine_shared_1.BoundaryLevel.Error && !isClaimedAuxiliaryFailure) || !detail.semanticType) {
	            continue;
	        }
	        const contents = blobMap.get(detail.relativePath);
	        if (!contents) {
	            continue;
	        }
	        const descriptor = {
	            type: detail.semanticType,
	            originalFileName: detail.relativePath,
	            contents,
	            validationResult: detail.fileValidationResult,
	        };
	        const fileName = sigma_engine_shared_1.Files.basenamePath(toForwardSlash(detail.relativePath));
	        switch (detail.semanticType) {
	            case 'board':
	                definition.boards[fileName] = descriptor;
	                break;
	            case 'schematic':
	                definition.schematics[detail.relativePath] = { ...descriptor };
	                break;
	            default:
	                otherFiles[detail.relativePath] = descriptor;
	                break;
	        }
	    }
	    return definition;
	}
	function buildBlobMap(files) {
	    return new Map(files.map((f) => [f.relativePath, f.contents]));
	}
	function projectDisplayName(anchorRelativePath) {
	    const base = basenameWithoutExtension(anchorRelativePath);
	    const dir = dirnameForward(anchorRelativePath);
	    if (dir === '' || dir === '.') {
	        return base;
	    }
	    return `${dir}/${base}`;
	}
	function endsWithExt(ext) {
	    const lower = ext.toLowerCase();
	    return (relativePath) => relativePath.toLowerCase().endsWith(lower);
	}
	function endsWithAnyExt(exts) {
	    const lowered = exts.map((e) => e.toLowerCase());
	    return (relativePath) => {
	        const lower = relativePath.toLowerCase();
	        return lowered.some((e) => lower.endsWith(e));
	    };
	}
	function detectAnchoredProjects(params) {
	    const { tool, toolDisplayName, files, fileDetails, primaryAnchorMatch, primaryAnchorLabel, secondaryAnchorMatch, secondaryAnchorLabel, } = params;
	    const toolFiles = fileDetails.filter((d) => d.cadTool === tool ||
	        (d.semanticType === 'other' &&
	            d.fileValidationResult?.isValid === false &&
	            d.fileValidationResult.claimedTools?.includes(tool) === true));
	    const warnings = [];
	    const blobMap = buildBlobMap(files);
	    if (toolFiles.length === 0) {
	        return { projects: [], warnings };
	    }
	    let anchors = toolFiles.filter((d) => primaryAnchorMatch(d.relativePath));
	    if (anchors.length === 0 && secondaryAnchorMatch) {
	        const fallback = toolFiles.filter((d) => secondaryAnchorMatch(d.relativePath));
	        if (fallback.length > 0) {
	            warnings.push(`No ${primaryAnchorLabel} file found; deriving ${toolDisplayName} project name from ${secondaryAnchorLabel ?? 'sidecar file'}. Upload the ${primaryAnchorLabel} file for a complete project.`);
	            anchors = fallback;
	        }
	    }
	    if (anchors.length === 0) {
	        warnings.push(`No ${toolDisplayName} project file (${primaryAnchorLabel}${secondaryAnchorLabel ? ` or ${secondaryAnchorLabel}` : ''}) found. Upload the project file for a complete project definition.`);
	        return {
	            projects: [buildProjectDefinition(tool, undefined, toolFiles, blobMap)],
	            warnings,
	        };
	    }
	    const groups = anchors.map((anchor) => ({
	        name: projectDisplayName(anchor.relativePath),
	        homeDir: dirnameForward(anchor.relativePath),
	        files: [],
	    }));
	    for (const detail of toolFiles) {
	        const fileDir = dirnameForward(detail.relativePath);
	        let best;
	        let bestLen = -1;
	        for (const group of groups) {
	            if (isSameOrDescendantDir(fileDir, group.homeDir) && group.homeDir.length > bestLen) {
	                best = group;
	                bestLen = group.homeDir.length;
	            }
	        }
	        (best ?? groups[0]).files.push(detail);
	    }
	    const projects = groups.map((g) => buildProjectDefinition(tool, g.name, g.files, blobMap));
	    return { projects, warnings };
	}
	
	return projectDetection;
}

var Util = {};

var hasRequiredUtil;

function requireUtil () {
	if (hasRequiredUtil) return Util;
	hasRequiredUtil = 1;
	Object.defineProperty(Util, "__esModule", { value: true });
	Util.ValidGridUnits = void 0;
	Util.convertGridDistanceUnits = convertGridDistanceUnits;
	Util.findComponentByFootprint = findComponentByFootprint;
	Util.dedupeWarnings = dedupeWarnings;
	Util.removeQuotes = removeQuotes;
	Util.cleanMetadataVal = cleanMetadataVal;
	Util.readMaxLines = readMaxLines;
	Util.getBlobTextClean = getBlobTextClean;
	Util.decodeWindows1252 = decodeWindows1252;
	Util.latin1ToWindows1252 = latin1ToWindows1252;
	Util.isWindows1252Representable = isWindows1252Representable;
	Util.decodeWindows1252Clean = decodeWindows1252Clean;
	Util.getBlobTextCleanAnsi = getBlobTextCleanAnsi;
	Util.bytesToLatin1 = bytesToLatin1;
	Util.getBlobTextCleanPartial = getBlobTextCleanPartial;
	Util.getValueFromMetadata = getValueFromMetadata;
	Util.isValidDistance = isValidDistance;
	Util.toFractionalPrecision = toFractionalPrecision;
	Util.maybeInvert = maybeInvert;
	Util.formatValueForGrid = formatValueForGrid;
	Util.formatPositionForGrid = formatPositionForGrid;
	const Transform_1 = requireTransform();
	const deepEqual = requireFastDeepEqual();
	const MM_PER_INCH = 25.4;
	Util.ValidGridUnits = ['inch', 'microns', 'mil', 'mm'];
	function convertGridDistanceUnits(distance, fromUnits, toUnits) {
	    if (fromUnits === toUnits) {
	        return distance;
	    }
	    let distanceMM;
	    switch (fromUnits) {
	        case 'inch':
	            distanceMM = distance * MM_PER_INCH;
	            break;
	        case 'mm':
	            distanceMM = distance;
	            break;
	        case 'mil':
	            distanceMM = (distance * MM_PER_INCH) / 1000;
	            break;
	        case 'microns':
	            distanceMM = distance / 1000;
	            break;
	        default:
	            throw new Error(`Invalid value ${fromUnits} for fromUnits`);
	    }
	    switch (toUnits) {
	        case 'inch':
	            return (distanceMM = distanceMM / MM_PER_INCH);
	        case 'mm':
	            return distanceMM;
	        case 'mil':
	            return (distanceMM = (distanceMM / MM_PER_INCH) * 1000);
	        case 'microns':
	            return (distanceMM = distanceMM * 1000);
	        default:
	            throw new Error(`Invalid value ${toUnits} for toUnits`);
	    }
	}
	function findComponentByFootprint(project, footprint, model) {
	    for (const component of Object.values(project.components)) {
	        for (const fp of component.footprints) {
	            if (fp.ref === footprint.ref) {
	                if (!deepEqual(fp, footprint)) {
	                    model.warnings.push(`Multiple definitions found for footprint '${footprint.name}'`);
	                }
	                return component;
	            }
	        }
	    }
	    for (const design of Object.values(project.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        for (const module of design.modules) {
	            if (module.drawProperties['Footprint']?.value === footprint.ref) {
	                project.components[module.componentRef].footprints.push(footprint);
	                return project.components[module.footprintRef];
	            }
	        }
	    }
	    return project.components[footprint.ref];
	}
	function dedupeWarnings(model) {
	    const warnings = new Set();
	    for (const warning of model.warnings) {
	        if (!warnings.has(warning)) {
	            warnings.add(warning);
	        }
	    }
	    model.warnings = Array.from(warnings);
	}
	function removeQuotes(value) {
	    return value?.replace(/^"|"$/g, '').trim();
	}
	function cleanMetadataVal(value) {
	    value = value?.replace(/^(?:\s|\\r|\\n)+|(?:\s|\\r|\\n)+$/gm, '').trim();
	    if (!value || value === '~') {
	        return undefined;
	    }
	    return value;
	}
	function readMaxLines(contents, maxLines) {
	    const lines = [];
	    let lineStart = 0;
	    for (let chrPos = 0; lines.length < maxLines && chrPos < contents.length; ++chrPos) {
	        if (contents[chrPos] === '\n') {
	            lines.push(contents.substring(lineStart, chrPos));
	            lineStart = chrPos + 1;
	        }
	    }
	    if (lines.length === 0) {
	        return [contents];
	    }
	    return lines;
	}
	async function getBlobTextClean(blob) {
	    return (await blob.text()).replace(/\r/g, '');
	}
	const windows1252C1Mappings = '\u20ac\u0081\u201a\u0192\u201e\u2026\u2020\u2021' +
	    '\u02c6\u2030\u0160\u2039\u0152\u008d\u017d\u008f' +
	    '\u0090\u2018\u2019\u201c\u201d\u2022\u2013\u2014' +
	    '\u02dc\u2122\u0161\u203a\u0153\u009d\u017e\u0178';
	function decodeWindows1252(bytes) {
	    return latin1ToWindows1252(bytesToLatin1(bytes));
	}
	function latin1ToWindows1252(byteString) {
	    return byteString.replace(/[\u0080-\u009f]/g, (char) => windows1252C1Mappings.charAt(char.charCodeAt(0) - 0x80));
	}
	const windows1252CodePoints = (() => {
	    const codePoints = new Set();
	    for (let byte = 0; byte <= 0xff; byte++) {
	        codePoints.add(byte < 0x80 || byte > 0x9f ? byte : windows1252C1Mappings.charCodeAt(byte - 0x80));
	    }
	    return codePoints;
	})();
	function isWindows1252Representable(value) {
	    for (const char of value) {
	        const codePoint = char.codePointAt(0);
	        if (codePoint === undefined || !windows1252CodePoints.has(codePoint)) {
	            return false;
	        }
	    }
	    return true;
	}
	function decodeWindows1252Clean(bytes) {
	    return decodeWindows1252(bytes).replace(/\r/g, '');
	}
	async function getBlobTextCleanAnsi(blob) {
	    return decodeWindows1252Clean(new Uint8Array(await blob.arrayBuffer()));
	}
	function bytesToLatin1(bytes) {
	    const chunkSize = 0x8000;
	    if (bytes.length <= chunkSize) {
	        return Reflect.apply(String.fromCharCode, null, bytes);
	    }
	    const chunks = [];
	    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
	        chunks.push(Reflect.apply(String.fromCharCode, null, bytes.subarray(offset, offset + chunkSize)));
	    }
	    return chunks.join('');
	}
	async function getBlobTextCleanPartial(blob, maxBytes = 65536) {
	    const slice = blob.slice(0, Math.min(maxBytes, blob.size));
	    return (await slice.text()).replace(/\r/g, '');
	}
	function getValueFromMetadata(currentValue, metaModule) {
	    const metadataRegex = /\${([^}]*)}/g;
	    if (metaModule && metaModule.metadata && metadataRegex.test(currentValue)) {
	        const valueKey = currentValue.substring(2, currentValue.length - 1);
	        if (valueKey in metaModule.metadata) {
	            return currentValue.replace(metadataRegex, (match, valueKey) => metaModule.metadata?.[valueKey]?.toString() ?? match);
	        }
	    }
	    return currentValue;
	}
	function isValidDistance(val) {
	    return typeof val === 'number' && Number.isFinite(val) && val > 0;
	}
	function toFractionalPrecision(val, precision, maxFractional = 8) {
	    const valStr = val.toString();
	    const [int, fractional] = valStr.split('.', 2);
	    const [, precisionFractionalStr] = precision.toString().split('.', 2);
	    const precisionFractional = Math.min(precisionFractionalStr?.length || 0, maxFractional);
	    let retVal;
	    if (!fractional) {
	        retVal = int + '.'.padEnd(precisionFractional + 1, '0');
	    }
	    else {
	        retVal = `${int}.${fractional.substring(0, precisionFractional).padEnd(precisionFractional, '0')}`;
	    }
	    if (retVal.endsWith('.')) {
	        return retVal.substring(0, retVal.length - 1);
	    }
	    return retVal;
	}
	function maybeInvert(value, invert) {
	    if (invert) {
	        return -value;
	    }
	    return value;
	}
	function formatValueForGrid(value, grid, roundToGridIntervals = true) {
	    if (isValidDistance(grid?.distance)) {
	        return toFractionalPrecision(convertGridDistanceUnits(roundToGridIntervals ? (0, Transform_1.roundToMultiple)(value, grid.distance) : value, 'mm', grid.units), grid.distance);
	    }
	    return undefined;
	}
	function formatPositionForGrid(drawingCoords, grid) {
	    if (isValidDistance(grid.distance)) {
	        return {
	            x: toFractionalPrecision(maybeInvert(convertGridDistanceUnits((0, Transform_1.roundToMultiple)(drawingCoords.x, grid.distance), 'mm', grid.units), grid.invertX), grid.distance),
	            y: toFractionalPrecision(maybeInvert(convertGridDistanceUnits((0, Transform_1.roundToMultiple)(drawingCoords.y, grid.distance), 'mm', grid.units), grid.invertY), grid.distance),
	        };
	    }
	    return undefined;
	}
	
	return Util;
}

var altiumRecordFile = {};

var hasRequiredAltiumRecordFile;

function requireAltiumRecordFile () {
	if (hasRequiredAltiumRecordFile) return altiumRecordFile;
	hasRequiredAltiumRecordFile = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AltiumRecordStackupType = exports.AltiumRecordRULType = exports.AltiumRecordDefinitions = void 0;
		exports.parseAltiumRecordFile = parseAltiumRecordFile;
		function parseAltiumRecord(record, allowUntyped) {
		    if (record.startsWith('|')) {
		        record = record.substring(1);
		    }
		    if (record.endsWith('|')) {
		        record = record.substring(0, record.length - 1);
		    }
		    const parts = record.split('|');
		    if (parts.length < 2) {
		        return null;
		    }
		    const result = {};
		    for (const part of parts) {
		        if (!part.length) {
		            continue;
		        }
		        if (!part.includes('=')) {
		            continue;
		        }
		        const key = part.substring(0, part.indexOf('=')).toUpperCase();
		        const value = part.substring(part.indexOf('=') + 1);
		        if (!value || value.length === 0) {
		            continue;
		        }
		        if (key === 'RECORD') {
		            const definition = exports.AltiumRecordDefinitions[value];
		            if (!definition) {
		                return null;
		            }
		            result.RECORD = value;
		        }
		        else {
		            result[key] = value;
		        }
		    }
		    if (!allowUntyped && !result.RECORD) {
		        return null;
		    }
		    return result;
		}
		function parseAltiumRecordFile(contents, supportedDefinitions = exports.AltiumRecordDefinitions, allowUnknown, implicitRecordType) {
		    const records = [];
		    const lines = contents.split('\n');
		    for (let line of lines) {
		        line = line.trim();
		        if (line.length === 0) {
		            continue;
		        }
		        const record = parseAltiumRecord(line, !!implicitRecordType);
		        if (!record) {
		            continue;
		        }
		        if (!record.RECORD && implicitRecordType) {
		            record.RECORD = implicitRecordType.name;
		        }
		        const definition = supportedDefinitions[record.RECORD] ?? implicitRecordType;
		        if (!definition) {
		            if (allowUnknown) {
		                records.push(record);
		            }
		            continue;
		        }
		        for (const property of definition.properties) {
		            if (property.required && record[property.name] === undefined) {
		                throw new Error(`Property ${property.name} is required for record ${record.RECORD}`);
		            }
		            if (record[property.name] === undefined) {
		                continue;
		            }
		            if (property.type === 'number') {
		                try {
		                    record[property.name] = parseFloat(record[property.name]);
		                }
		                catch {
		                    throw new Error(`Property ${property.name} is not a number for record ${record.RECORD}`);
		                }
		            }
		            if (property.type === 'boolean') {
		                record[property.name] = record[property.name].toLowerCase().trim() === 'true';
		            }
		            if (typeof record[property.name] !== property.type) {
		                throw new Error(`Property ${property.name} is required for record ${record.RECORD}`);
		            }
		        }
		        records.push(record);
		    }
		    return records;
		}
		exports.AltiumRecordDefinitions = {
		    BOM: {
		        name: 'BOM',
		        properties: [
		            { name: 'VERSION', type: 'string' },
		            { name: 'FILENAME', type: 'string' },
		            { name: 'KIND', type: 'string' },
		            { name: 'DATE', type: 'string' },
		            { name: 'TIME', type: 'string' },
		            { name: 'CURRENCY', type: 'string' },
		            { name: 'PRODUCTIONQUANTITY', type: 'number' },
		            { name: 'PERUNITTARGETPRICE', type: 'number' },
		            { name: 'PERUNITACTUALPRICE', type: 'number' },
		        ],
		    },
		    GeneralOptions: {
		        name: 'GeneralOptions',
		        properties: [
		            { name: 'OPENEXPORTED', type: 'boolean' },
		            { name: 'ADDTOPROJECT', type: 'boolean' },
		            { name: 'FORCEFIT', type: 'boolean' },
		            { name: 'NOTFITTED', type: 'boolean' },
		            { name: 'TEMPLATEFILENAME', type: 'string' },
		            { name: 'BATCHMODE', type: 'number' },
		            { name: 'SUPPLIERPRODQTY', type: 'number' },
		            { name: 'SUPPLIERAUTOQTY', type: 'boolean' },
		            { name: 'SUPPLIERUSECACHEDPRICING', type: 'boolean' },
		            { name: 'SUPPLIERCURRENCY', type: 'string' },
		            { name: 'SOLUTIONSPERITEM', type: 'number' },
		            { name: 'SUPPLIERSPERSOLUTION', type: 'number' },
		            { name: 'DISPLAYTOTALROW', type: 'boolean' },
		            { name: 'DISPLAYSUBTOTALSFORGROUPS', type: 'boolean' },
		            { name: 'INCLUDEPCBPARAMETERS', type: 'boolean' },
		            { name: 'INCLUDEDBPARAMETERS', type: 'boolean' },
		            { name: 'INCLUDEVAULTPARAMETERS', type: 'boolean' },
		            { name: 'INCLUDECLOUDPARAMETERS', type: 'boolean' },
		            { name: 'INCLUDEDOCUMENTPARAMETERS', type: 'boolean' },
		            { name: 'FILTEROUTRISKYSOLUTIONS', type: 'boolean' },
		            { name: 'DESIGNATORGROUPING', type: 'string' },
		        ],
		    },
		    SortOrderOptions: {
		        name: 'SortOrderOptions',
		        properties: [{ name: 'SORTORDER', type: 'string' }],
		    },
		    GroupOrderOptions: {
		        name: 'GroupOrderOptions',
		        properties: [{ name: 'GROUPORDER', type: 'string' }],
		    },
		    VisibleOrderOptions: {
		        name: 'VisibleOrderOptions',
		        properties: [{ name: 'VISIBLEORDER', type: 'string' }],
		    },
		    VisibleOrderOptions2: {
		        name: 'VisibleOrderOptions2',
		        properties: [
		            { name: 'BASEVIEW', type: 'string' },
		            { name: 'FLATVIEW', type: 'string' },
		            { name: 'CONSOLIDATEDVIEW', type: 'string' },
		        ],
		    },
		    CustomColumns: {
		        name: 'CustomColumns',
		        properties: [{ name: 'CUSTOMCOLUMNS', type: 'string' }],
		    },
		    ColumnsAliases: {
		        name: 'ColumnsAliases',
		        properties: [{ name: 'COLUMNSALIASES', type: 'string' }],
		    },
		    FilterOptions: {
		        name: 'FilterOptions',
		        properties: [{ name: 'FILTER', type: 'string' }],
		    },
		    CheckGroupCompParamsOptions: {
		        name: 'CheckGroupCompParamsOptions',
		        properties: [{ name: 'CHECKGROUPCOMPPARAMS', type: 'string' }],
		    },
		    ManufacturerLinkOptions: {
		        name: 'ManufacturerLinkOptions',
		        properties: [{ name: 'MANUFACTURERLINKPARAMETERS', type: 'string' }],
		    },
		    FavoriteSuppliers: {
		        name: 'FavoriteSuppliers',
		        properties: [
		            { name: 'SUPPLIERS', type: 'string' },
		            { name: 'USESUPPLIERORDER', type: 'boolean' },
		        ],
		    },
		    CheckOptions: {
		        name: 'CheckOptions',
		        properties: [{ name: 'CHECKLEVELS', type: 'string' }],
		    },
		    LineNumberingOptions: {
		        name: 'LineNumberingOptions',
		        properties: [
		            { name: 'STARTVALUE', type: 'string' },
		            { name: 'INCREMENT', type: 'string' },
		        ],
		    },
		    Catalog: {
		        name: 'Catalog',
		        properties: [{ name: 'DBLINKFILEPATH', type: 'string' }],
		    },
		    CatalogItem: {
		        name: 'CatalogItem',
		        properties: [
		            { name: 'ITEMTYPE', type: 'string' },
		            { name: 'UNIQUEID', type: 'string' },
		            { name: 'DESIGNITEMID', type: 'string' },
		            { name: 'ITEMSOURCE', type: 'string' },
		            { name: 'STATUS', type: 'string' },
		            { name: 'DESCRIPTION', type: 'string' },
		            { name: 'USERCOMMENTS', type: 'string' },
		            { name: 'NOTE', type: 'string' },
		            { name: 'LINENUMBER', type: 'string' },
		            { name: 'SELECTEDPARAMETERSHASH', type: 'string' },
		            { name: 'COMPONENTPARAMETERS', type: 'string' },
		        ],
		    },
		    TopLevelDocument: {
		        name: 'TopLevelDocument',
		        properties: [
		            { name: 'FILENAME', type: 'string', required: true },
		            { name: 'SHEETNUMBER', type: 'number' },
		        ],
		    },
		    NoMainPathDocument: {
		        name: 'NoMainPathDocument',
		        properties: [
		            { name: 'SOURCEDOCUMENT', type: 'string' },
		            { name: 'FILENAME', type: 'string', required: true },
		            { name: 'SHEETNUMBER', type: 'number' },
		        ],
		    },
		    SheetSymbol: {
		        name: 'SheetSymbol',
		        properties: [
		            { name: 'FILENAME', type: 'string', required: true },
		            { name: 'SOURCEDOCUMENT', type: 'string' },
		            { name: 'DESIGNATOR', type: 'string' },
		            { name: 'SCHDESIGNATOR', type: 'string' },
		            { name: 'SHEETNUMBER', type: 'string' },
		            { name: 'SYMBOLTYPE', type: 'string' },
		            { name: 'RAWFILENAME', type: 'string' },
		            { name: 'DESIGNITEMID', type: 'string' },
		            { name: 'SOURCELIBRARYNAME', type: 'string' },
		            { name: 'OBJECTKIND', type: 'string' },
		            { name: 'REVISIONGUID', type: 'string' },
		            { name: 'ITEMGUID', type: 'string' },
		            { name: 'VAULTGUID', type: 'string' },
		        ],
		    },
		};
		exports.AltiumRecordRULType = {
		    name: 'RUL',
		    properties: [
		        { name: 'SELECTION', type: 'boolean' },
		        { name: 'LAYER', type: 'string' },
		        { name: 'LOCKED', type: 'boolean' },
		        { name: 'POLYGONOUTLINE', type: 'boolean' },
		        { name: 'USERROUTED', type: 'boolean' },
		        { name: 'KEEPOUT', type: 'boolean' },
		        { name: 'UNIONINDEX', type: 'number' },
		        { name: 'RULEKIND', type: 'string' },
		        { name: 'NETSCOPE', type: 'string' },
		        { name: 'LAYERKIND', type: 'string' },
		        { name: 'SCOPE1EXPRESSION', type: 'string' },
		        { name: 'SCOPE2EXPRESSION', type: 'string' },
		        { name: 'NAME', type: 'string' },
		        { name: 'ENABLED', type: 'boolean' },
		        { name: 'PRIORITY', type: 'number' },
		        { name: 'COMMENT', type: 'string' },
		        { name: 'UNIQUEID', type: 'string' },
		        { name: 'DEFINEDBYLOGICALDOCUMENT', type: 'boolean' },
		        { name: 'BGADIR', type: 'string' },
		        { name: 'BGAVIAMODE', type: 'string' },
		        { name: 'FANOUTSTYLE', type: 'string' },
		        { name: 'FANOUTDIRECTION', type: 'string' },
		        { name: 'VIAGRID', type: 'string' },
		    ],
		};
		exports.AltiumRecordStackupType = {
		    name: 'STACKUP',
		    properties: [
		        { name: 'STACKUPVERSION', type: 'number', required: true },
		        { name: 'STACKUPDOCUMENTID', type: 'string' },
		        { name: 'STACKUPDOCUMENTREVISIONID', type: 'string' },
		    ],
		};
		
	} (altiumRecordFile));
	return altiumRecordFile;
}

var projectFileParser = {};

var altiumProjectFileTypes = {};

var AltiumEnums = {};

var hasRequiredAltiumEnums;

function requireAltiumEnums () {
	if (hasRequiredAltiumEnums) return AltiumEnums;
	hasRequiredAltiumEnums = 1;
	Object.defineProperty(AltiumEnums, "__esModule", { value: true });
	AltiumEnums.ComponentKindSchema = AltiumEnums.ComponentKind = AltiumEnums.LabelJustificationSchema = AltiumEnums.LabelJustification = AltiumEnums.PortAlignmentSchema = AltiumEnums.PortAlignment = AltiumEnums.PortStyleSchema = AltiumEnums.PortStyle = AltiumEnums.TextFrameAlignmentSchema = AltiumEnums.TextFrameAlignment = AltiumEnums.SheetSymbolSideSchema = AltiumEnums.SheetSymbolSide = AltiumEnums.PortIOTypeSchema = AltiumEnums.PortIOType = AltiumEnums.OffSheetConnectorStyleSchema = AltiumEnums.OffSheetConnectorStyle = AltiumEnums.PowerPortStyleSchema = AltiumEnums.PowerPortStyle = AltiumEnums.FillModeSchema = AltiumEnums.FillMode = AltiumEnums.LineWidthSchema = AltiumEnums.LineWidth = AltiumEnums.JustificationSchema = AltiumEnums.Justification = AltiumEnums.PinLengthSchema = AltiumEnums.PinLength = AltiumEnums.ElectricalTypeSchema = AltiumEnums.ElectricalType = AltiumEnums.CoordinateSchema = AltiumEnums.ColorSchema = AltiumEnums.PortOrientationSchema = AltiumEnums.PortOrientation = AltiumEnums.OrientationSchema = AltiumEnums.Orientation = void 0;
	AltiumEnums.convertPowerPortOrientation = convertPowerPortOrientation;
	AltiumEnums.isKnownComponentKind = isKnownComponentKind;
	AltiumEnums.preprocessEnumValue = preprocessEnumValue;
	const zod_1 = /*@__PURE__*/ requireZod();
	var Orientation;
	(function (Orientation) {
	    Orientation[Orientation["Rotate0"] = 0] = "Rotate0";
	    Orientation[Orientation["Rotate90"] = 1] = "Rotate90";
	    Orientation[Orientation["Rotate180"] = 2] = "Rotate180";
	    Orientation[Orientation["Rotate270"] = 3] = "Rotate270";
	})(Orientation || (AltiumEnums.Orientation = Orientation = {}));
	AltiumEnums.OrientationSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(Orientation));
	var PortOrientation;
	(function (PortOrientation) {
	    PortOrientation[PortOrientation["RIGHTWARDS"] = 0] = "RIGHTWARDS";
	    PortOrientation[PortOrientation["UPWARDS"] = 1] = "UPWARDS";
	    PortOrientation[PortOrientation["LEFTWARDS"] = 2] = "LEFTWARDS";
	    PortOrientation[PortOrientation["DOWNWARDS"] = 3] = "DOWNWARDS";
	})(PortOrientation || (AltiumEnums.PortOrientation = PortOrientation = {}));
	AltiumEnums.PortOrientationSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(PortOrientation));
	function convertPowerPortOrientation(val) {
	    switch (val) {
	        case PortOrientation.RIGHTWARDS:
	            return Orientation.Rotate180;
	        case PortOrientation.UPWARDS:
	            return Orientation.Rotate90;
	        case PortOrientation.LEFTWARDS:
	            return Orientation.Rotate0;
	        case PortOrientation.DOWNWARDS:
	            return Orientation.Rotate270;
	    }
	}
	AltiumEnums.ColorSchema = zod_1.z
	    .object({
	    r: zod_1.z.number(),
	    g: zod_1.z.number(),
	    b: zod_1.z.number(),
	})
	    .strict();
	AltiumEnums.CoordinateSchema = zod_1.z
	    .object({
	    x: zod_1.z.number(),
	    y: zod_1.z.number(),
	})
	    .strict();
	var ElectricalType;
	(function (ElectricalType) {
	    ElectricalType[ElectricalType["Input"] = 0] = "Input";
	    ElectricalType[ElectricalType["IO"] = 1] = "IO";
	    ElectricalType[ElectricalType["Output"] = 2] = "Output";
	    ElectricalType[ElectricalType["OpenCollector"] = 3] = "OpenCollector";
	    ElectricalType[ElectricalType["Passive"] = 4] = "Passive";
	    ElectricalType[ElectricalType["HiZ"] = 5] = "HiZ";
	    ElectricalType[ElectricalType["OpenEmitter"] = 6] = "OpenEmitter";
	    ElectricalType[ElectricalType["Power"] = 7] = "Power";
	})(ElectricalType || (AltiumEnums.ElectricalType = ElectricalType = {}));
	AltiumEnums.ElectricalTypeSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(ElectricalType));
	var PinLength;
	(function (PinLength) {
	    PinLength[PinLength["Short"] = 0] = "Short";
	    PinLength[PinLength["Middle"] = 1] = "Middle";
	    PinLength[PinLength["Long"] = 2] = "Long";
	})(PinLength || (AltiumEnums.PinLength = PinLength = {}));
	AltiumEnums.PinLengthSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(PinLength));
	var Justification;
	(function (Justification) {
	    Justification[Justification["BottomLeft"] = 0] = "BottomLeft";
	    Justification[Justification["BottomCenter"] = 1] = "BottomCenter";
	    Justification[Justification["BottomRight"] = 2] = "BottomRight";
	    Justification[Justification["CenterLeft"] = 3] = "CenterLeft";
	    Justification[Justification["Center"] = 4] = "Center";
	    Justification[Justification["CenterRight"] = 5] = "CenterRight";
	    Justification[Justification["TopLeft"] = 6] = "TopLeft";
	    Justification[Justification["TopCenter"] = 7] = "TopCenter";
	    Justification[Justification["TopRight"] = 8] = "TopRight";
	})(Justification || (AltiumEnums.Justification = Justification = {}));
	AltiumEnums.JustificationSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(Justification));
	var LineWidth;
	(function (LineWidth) {
	    LineWidth[LineWidth["Smallest"] = 0] = "Smallest";
	    LineWidth[LineWidth["Small"] = 1] = "Small";
	    LineWidth[LineWidth["Medium"] = 2] = "Medium";
	    LineWidth[LineWidth["Large"] = 3] = "Large";
	})(LineWidth || (AltiumEnums.LineWidth = LineWidth = {}));
	AltiumEnums.LineWidthSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(LineWidth));
	var FillMode;
	(function (FillMode) {
	    FillMode[FillMode["None"] = 0] = "None";
	    FillMode[FillMode["Solid"] = 1] = "Solid";
	    FillMode[FillMode["Transparent"] = 2] = "Transparent";
	})(FillMode || (AltiumEnums.FillMode = FillMode = {}));
	AltiumEnums.FillModeSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(FillMode));
	var PowerPortStyle;
	(function (PowerPortStyle) {
	    PowerPortStyle[PowerPortStyle["Circle"] = 0] = "Circle";
	    PowerPortStyle[PowerPortStyle["Arrow"] = 1] = "Arrow";
	    PowerPortStyle[PowerPortStyle["Bar"] = 2] = "Bar";
	    PowerPortStyle[PowerPortStyle["Wave"] = 3] = "Wave";
	    PowerPortStyle[PowerPortStyle["PowerGround"] = 4] = "PowerGround";
	    PowerPortStyle[PowerPortStyle["SignalGround"] = 5] = "SignalGround";
	    PowerPortStyle[PowerPortStyle["Earth"] = 6] = "Earth";
	    PowerPortStyle[PowerPortStyle["GostArrow"] = 7] = "GostArrow";
	    PowerPortStyle[PowerPortStyle["GostPowerGround"] = 8] = "GostPowerGround";
	    PowerPortStyle[PowerPortStyle["GostEarth"] = 9] = "GostEarth";
	    PowerPortStyle[PowerPortStyle["GostBar"] = 10] = "GostBar";
	})(PowerPortStyle || (AltiumEnums.PowerPortStyle = PowerPortStyle = {}));
	AltiumEnums.PowerPortStyleSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(PowerPortStyle));
	var OffSheetConnectorStyle;
	(function (OffSheetConnectorStyle) {
	    OffSheetConnectorStyle[OffSheetConnectorStyle["LeftArrow"] = 0] = "LeftArrow";
	    OffSheetConnectorStyle[OffSheetConnectorStyle["RightArrow"] = 1] = "RightArrow";
	})(OffSheetConnectorStyle || (AltiumEnums.OffSheetConnectorStyle = OffSheetConnectorStyle = {}));
	AltiumEnums.OffSheetConnectorStyleSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(OffSheetConnectorStyle));
	var PortIOType;
	(function (PortIOType) {
	    PortIOType[PortIOType["Unspecified"] = 0] = "Unspecified";
	    PortIOType[PortIOType["Output"] = 1] = "Output";
	    PortIOType[PortIOType["Input"] = 2] = "Input";
	    PortIOType[PortIOType["Bidirectional"] = 3] = "Bidirectional";
	})(PortIOType || (AltiumEnums.PortIOType = PortIOType = {}));
	AltiumEnums.PortIOTypeSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(PortIOType));
	var SheetSymbolSide;
	(function (SheetSymbolSide) {
	    SheetSymbolSide[SheetSymbolSide["Left"] = 0] = "Left";
	    SheetSymbolSide[SheetSymbolSide["Right"] = 1] = "Right";
	    SheetSymbolSide[SheetSymbolSide["Top"] = 2] = "Top";
	    SheetSymbolSide[SheetSymbolSide["Bottom"] = 3] = "Bottom";
	})(SheetSymbolSide || (AltiumEnums.SheetSymbolSide = SheetSymbolSide = {}));
	AltiumEnums.SheetSymbolSideSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(SheetSymbolSide));
	var TextFrameAlignment;
	(function (TextFrameAlignment) {
	    TextFrameAlignment[TextFrameAlignment["Left"] = 1] = "Left";
	    TextFrameAlignment[TextFrameAlignment["Center"] = 2] = "Center";
	    TextFrameAlignment[TextFrameAlignment["Right"] = 3] = "Right";
	})(TextFrameAlignment || (AltiumEnums.TextFrameAlignment = TextFrameAlignment = {}));
	AltiumEnums.TextFrameAlignmentSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(TextFrameAlignment));
	var PortStyle;
	(function (PortStyle) {
	    PortStyle[PortStyle["NoneHorizontal"] = 0] = "NoneHorizontal";
	    PortStyle[PortStyle["Left"] = 1] = "Left";
	    PortStyle[PortStyle["Right"] = 2] = "Right";
	    PortStyle[PortStyle["LeftRight"] = 3] = "LeftRight";
	    PortStyle[PortStyle["NoneVertical"] = 4] = "NoneVertical";
	    PortStyle[PortStyle["Top"] = 5] = "Top";
	    PortStyle[PortStyle["Bottom"] = 6] = "Bottom";
	    PortStyle[PortStyle["TopBottom"] = 7] = "TopBottom";
	})(PortStyle || (AltiumEnums.PortStyle = PortStyle = {}));
	AltiumEnums.PortStyleSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(PortStyle));
	var PortAlignment;
	(function (PortAlignment) {
	    PortAlignment[PortAlignment["Center"] = 0] = "Center";
	    PortAlignment[PortAlignment["Right"] = 1] = "Right";
	    PortAlignment[PortAlignment["Left"] = 2] = "Left";
	})(PortAlignment || (AltiumEnums.PortAlignment = PortAlignment = {}));
	AltiumEnums.PortAlignmentSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(PortAlignment));
	var LabelJustification;
	(function (LabelJustification) {
	    LabelJustification[LabelJustification["BottomLeft"] = 0] = "BottomLeft";
	    LabelJustification[LabelJustification["BottomCenter"] = 1] = "BottomCenter";
	    LabelJustification[LabelJustification["BottomRight"] = 2] = "BottomRight";
	    LabelJustification[LabelJustification["CenterLeft"] = 3] = "CenterLeft";
	    LabelJustification[LabelJustification["CenterCenter"] = 4] = "CenterCenter";
	    LabelJustification[LabelJustification["CenterRight"] = 5] = "CenterRight";
	    LabelJustification[LabelJustification["TopLeft"] = 6] = "TopLeft";
	    LabelJustification[LabelJustification["TopCenter"] = 7] = "TopCenter";
	    LabelJustification[LabelJustification["TopRight"] = 8] = "TopRight";
	})(LabelJustification || (AltiumEnums.LabelJustification = LabelJustification = {}));
	AltiumEnums.LabelJustificationSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(LabelJustification));
	var ComponentKind;
	(function (ComponentKind) {
	    ComponentKind[ComponentKind["Standard"] = 0] = "Standard";
	    ComponentKind[ComponentKind["Mechanical"] = 1] = "Mechanical";
	    ComponentKind[ComponentKind["Graphical"] = 2] = "Graphical";
	    ComponentKind[ComponentKind["NetTieInBOM"] = 3] = "NetTieInBOM";
	    ComponentKind[ComponentKind["NetTieNoBOM"] = 4] = "NetTieNoBOM";
	    ComponentKind[ComponentKind["StandardNoBOM"] = 5] = "StandardNoBOM";
	    ComponentKind[ComponentKind["Jumper"] = 6] = "Jumper";
	})(ComponentKind || (AltiumEnums.ComponentKind = ComponentKind = {}));
	AltiumEnums.ComponentKindSchema = zod_1.z.preprocess(preprocessEnumValue, zod_1.z.nativeEnum(ComponentKind));
	const componentKindValues = new Set(Object.values(ComponentKind).filter((value) => typeof value === 'number'));
	function isKnownComponentKind(value) {
	    return componentKindValues.has(value);
	}
	function preprocessEnumValue(val) {
	    if (typeof val === 'string') {
	        if (/^\d+$/.test(val)) {
	            return parseInt(val, 10);
	        }
	    }
	    return val;
	}
	
	return AltiumEnums;
}

var hasRequiredAltiumProjectFileTypes;

function requireAltiumProjectFileTypes () {
	if (hasRequiredAltiumProjectFileTypes) return altiumProjectFileTypes;
	hasRequiredAltiumProjectFileTypes = 1;
	Object.defineProperty(altiumProjectFileTypes, "__esModule", { value: true });
	altiumProjectFileTypes.AltiumVariationKind = altiumProjectFileTypes.AnnotateProcessLocationSchema = altiumProjectFileTypes.AnnotateProcessLocation = altiumProjectFileTypes.AnnotateSortOrderSchema = altiumProjectFileTypes.AnnotateSortOrder = altiumProjectFileTypes.NameNetsHierarchicallyModeSchema = altiumProjectFileTypes.NameNetsHierarchicallyMode = altiumProjectFileTypes.PowerPortNamesTakePriorityModeSchema = altiumProjectFileTypes.PowerPortNamesTakePriorityMode = altiumProjectFileTypes.HierarchyModeSchema = altiumProjectFileTypes.HierarchyMode = altiumProjectFileTypes.ChannelRoomNamingStyleSchema = altiumProjectFileTypes.ChannelRoomNamingStyle = void 0;
	altiumProjectFileTypes.emptyAltiumProjectFile = emptyAltiumProjectFile;
	const zod_1 = /*@__PURE__*/ requireZod();
	const AltiumEnums_1 = requireAltiumEnums();
	const altiumValidation_1 = requireAltiumValidation();
	function emptyAltiumProjectFile(filename = '') {
	    return {
	        [altiumValidation_1.AltiumProjectMetadataKey.FileName]: filename,
	        Design: {},
	        Preferences: {},
	        GeneratedDocument: [],
	        Configuration: [],
	        Document: [],
	        Parameter: [],
	        DiffPairSuffix: [],
	    };
	}
	var ChannelRoomNamingStyle;
	(function (ChannelRoomNamingStyle) {
	    ChannelRoomNamingStyle[ChannelRoomNamingStyle["FlatNumericWithNames"] = 0] = "FlatNumericWithNames";
	    ChannelRoomNamingStyle[ChannelRoomNamingStyle["FlatAlphaWithNames"] = 1] = "FlatAlphaWithNames";
	    ChannelRoomNamingStyle[ChannelRoomNamingStyle["NumericNamePath"] = 2] = "NumericNamePath";
	    ChannelRoomNamingStyle[ChannelRoomNamingStyle["AlphaNamePath"] = 3] = "AlphaNamePath";
	    ChannelRoomNamingStyle[ChannelRoomNamingStyle["MixedNamePath"] = 4] = "MixedNamePath";
	})(ChannelRoomNamingStyle || (altiumProjectFileTypes.ChannelRoomNamingStyle = ChannelRoomNamingStyle = {}));
	altiumProjectFileTypes.ChannelRoomNamingStyleSchema = zod_1.default.preprocess(AltiumEnums_1.preprocessEnumValue, zod_1.default.nativeEnum(ChannelRoomNamingStyle));
	var HierarchyMode;
	(function (HierarchyMode) {
	    HierarchyMode[HierarchyMode["Automatic"] = 0] = "Automatic";
	    HierarchyMode[HierarchyMode["Flat"] = 1] = "Flat";
	    HierarchyMode[HierarchyMode["Hierarchical"] = 2] = "Hierarchical";
	    HierarchyMode[HierarchyMode["Global"] = 3] = "Global";
	    HierarchyMode[HierarchyMode["StrictHierarchical"] = 4] = "StrictHierarchical";
	})(HierarchyMode || (altiumProjectFileTypes.HierarchyMode = HierarchyMode = {}));
	altiumProjectFileTypes.HierarchyModeSchema = zod_1.default.preprocess(AltiumEnums_1.preprocessEnumValue, zod_1.default.nativeEnum(HierarchyMode));
	var PowerPortNamesTakePriorityMode;
	(function (PowerPortNamesTakePriorityMode) {
	    PowerPortNamesTakePriorityMode[PowerPortNamesTakePriorityMode["No"] = 0] = "No";
	    PowerPortNamesTakePriorityMode[PowerPortNamesTakePriorityMode["Yes"] = 1] = "Yes";
	})(PowerPortNamesTakePriorityMode || (altiumProjectFileTypes.PowerPortNamesTakePriorityMode = PowerPortNamesTakePriorityMode = {}));
	altiumProjectFileTypes.PowerPortNamesTakePriorityModeSchema = zod_1.default.preprocess(AltiumEnums_1.preprocessEnumValue, zod_1.default.nativeEnum(PowerPortNamesTakePriorityMode).default(PowerPortNamesTakePriorityMode.No));
	var NameNetsHierarchicallyMode;
	(function (NameNetsHierarchicallyMode) {
	    NameNetsHierarchicallyMode[NameNetsHierarchicallyMode["No"] = 0] = "No";
	    NameNetsHierarchicallyMode[NameNetsHierarchicallyMode["Yes"] = 1] = "Yes";
	})(NameNetsHierarchicallyMode || (altiumProjectFileTypes.NameNetsHierarchicallyMode = NameNetsHierarchicallyMode = {}));
	altiumProjectFileTypes.NameNetsHierarchicallyModeSchema = zod_1.default.preprocess(AltiumEnums_1.preprocessEnumValue, zod_1.default.nativeEnum(NameNetsHierarchicallyMode).default(NameNetsHierarchicallyMode.No));
	var AnnotateSortOrder;
	(function (AnnotateSortOrder) {
	    AnnotateSortOrder[AnnotateSortOrder["UpThenAcross"] = 0] = "UpThenAcross";
	    AnnotateSortOrder[AnnotateSortOrder["DownThenAcross"] = 1] = "DownThenAcross";
	    AnnotateSortOrder[AnnotateSortOrder["AcrossThenUp"] = 2] = "AcrossThenUp";
	    AnnotateSortOrder[AnnotateSortOrder["AcrossThenDown"] = 3] = "AcrossThenDown";
	})(AnnotateSortOrder || (altiumProjectFileTypes.AnnotateSortOrder = AnnotateSortOrder = {}));
	altiumProjectFileTypes.AnnotateSortOrderSchema = zod_1.default.preprocess(AltiumEnums_1.preprocessEnumValue, zod_1.default.nativeEnum(AnnotateSortOrder).default(AnnotateSortOrder.AcrossThenUp));
	var AnnotateProcessLocation;
	(function (AnnotateProcessLocation) {
	    AnnotateProcessLocation[AnnotateProcessLocation["Designator"] = 0] = "Designator";
	    AnnotateProcessLocation[AnnotateProcessLocation["Part"] = 1] = "Part";
	})(AnnotateProcessLocation || (altiumProjectFileTypes.AnnotateProcessLocation = AnnotateProcessLocation = {}));
	altiumProjectFileTypes.AnnotateProcessLocationSchema = zod_1.default.preprocess(AltiumEnums_1.preprocessEnumValue, zod_1.default.nativeEnum(AnnotateProcessLocation).default(AnnotateProcessLocation.Designator));
	var AltiumVariationKind;
	(function (AltiumVariationKind) {
	    AltiumVariationKind[AltiumVariationKind["Fitted"] = 0] = "Fitted";
	    AltiumVariationKind[AltiumVariationKind["NotFitted"] = 1] = "NotFitted";
	    AltiumVariationKind[AltiumVariationKind["AlternatePart"] = 2] = "AlternatePart";
	})(AltiumVariationKind || (altiumProjectFileTypes.AltiumVariationKind = AltiumVariationKind = {}));
	
	return altiumProjectFileTypes;
}

var altiumBlockParser = {};

var hasRequiredAltiumBlockParser;

function requireAltiumBlockParser () {
	if (hasRequiredAltiumBlockParser) return altiumBlockParser;
	hasRequiredAltiumBlockParser = 1;
	Object.defineProperty(altiumBlockParser, "__esModule", { value: true });
	altiumBlockParser.parseBlocks = parseBlocks;
	function parseBlocks(contents, rawValueBlockNames = []) {
	    const blocks = [];
	    const lines = contents.split(/\r?\n/);
	    let currentBlock;
	    for (const line of lines) {
	        const trimmedLine = line.trim();
	        if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
	            if (currentBlock) {
	                blocks.push(currentBlock);
	            }
	            currentBlock = {
	                blockName: trimmedLine.substring(1, trimmedLine.length - 1),
	                blockContents: {},
	            };
	            const match = currentBlock.blockName.match(/^([a-zA-Z]+)[0-9]+$/);
	            if (match) {
	                currentBlock.blockName = match[1];
	                currentBlock.blockContents.id = match[0];
	            }
	        }
	        else if (currentBlock) {
	            const equalsIndex = trimmedLine.indexOf('=');
	            if (equalsIndex > 0) {
	                const key = trimmedLine.substring(0, equalsIndex).trim();
	                const value = rawValueBlockNames.includes(currentBlock.blockName) ?
	                    line.substring(line.indexOf('=') + 1)
	                    : trimmedLine.substring(equalsIndex + 1).trim();
	                if (key) {
	                    if (Object.hasOwn(currentBlock.blockContents, key)) {
	                        currentBlock.duplicateKeys ??= [];
	                        currentBlock.duplicateKeys.push(key);
	                    }
	                    currentBlock.blockContents[key] = value;
	                }
	            }
	        }
	    }
	    if (currentBlock) {
	        blocks.push(currentBlock);
	    }
	    return blocks;
	}
	
	return altiumBlockParser;
}

var hasRequiredProjectFileParser;

function requireProjectFileParser () {
	if (hasRequiredProjectFileParser) return projectFileParser;
	hasRequiredProjectFileParser = 1;
	Object.defineProperty(projectFileParser, "__esModule", { value: true });
	projectFileParser.parseAltiumProjectFile = parseAltiumProjectFile;
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const altiumBlockParser_1 = requireAltiumBlockParser();
	const zod_1 = /*@__PURE__*/ requireZod();
	const AltiumDiffPairSuffixSchema = zod_1.default.object({
	    Positive: zod_1.default.string(),
	    Negative: zod_1.default.string(),
	});
	function parseVariationString(variationStr) {
	    const fields = parsePipeDelimitedFields(variationStr);
	    return {
	        Designator: fields['Designator'] ?? '',
	        UniqueId: fields['UniqueId'] ?? '',
	        Kind: Number(fields['Kind'] ?? 0),
	        AlternatePart: fields['AlternatePart'] ?? '',
	        AltLibLink_DesignItemID: fields['AltLibLink_DesignItemID'],
	    };
	}
	function parsePipeDelimitedFields(str) {
	    const fields = {};
	    for (const pair of str.split('|')) {
	        const eqIndex = pair.indexOf('=');
	        if (eqIndex !== -1) {
	            fields[pair.substring(0, eqIndex)] = pair.substring(eqIndex + 1);
	        }
	    }
	    return fields;
	}
	function parseProjectVariant(raw) {
	    const variationCount = Number(raw['VariationCount'] ?? 0);
	    const variations = [];
	    for (let i = 1; i <= variationCount; i++) {
	        const variationStr = raw[`Variation${i}`];
	        if (variationStr) {
	            variations.push(parseVariationString(variationStr));
	        }
	    }
	    const paramVariationCount = Number(raw['ParamVariationCount'] ?? 0);
	    const paramVariations = [];
	    for (let i = 1; i <= paramVariationCount; i++) {
	        const paramVariationStr = raw[`ParamVariation${i}`];
	        if (paramVariationStr) {
	            const fields = parsePipeDelimitedFields(paramVariationStr);
	            paramVariations.push({
	                Designator: raw[`ParamDesignator${i}`] ?? '',
	                ParameterName: fields['ParameterName'] ?? '',
	                VariantValue: fields['VariantValue'] ?? '',
	            });
	        }
	    }
	    return {
	        Description: raw['Description'] ?? '',
	        UniqueId: raw['UniqueId'] ?? '',
	        AllowFabrication: raw['AllowFabrication'] === '1',
	        ParameterCount: Number(raw['ParameterCount'] ?? 0),
	        VariationCount: variationCount,
	        ParamVariationCount: paramVariationCount,
	        Variations: variations,
	        ParamVariations: paramVariations,
	    };
	}
	function parseAnnotateSortOrder(value) {
	    const result = altiumProjectFileTypes_1.AnnotateSortOrderSchema.safeParse(value);
	    return result.success ? result.data : altiumProjectFileTypes_1.AnnotateSortOrder.AcrossThenUp;
	}
	function parseAnnotateProcessLocation(value) {
	    const result = altiumProjectFileTypes_1.AnnotateProcessLocationSchema.safeParse(value);
	    return result.success ? result.data : altiumProjectFileTypes_1.AnnotateProcessLocation.Designator;
	}
	function parseAltiumProjectFile(filename, contents) {
	    const project = (0, altiumProjectFileTypes_1.emptyAltiumProjectFile)(filename);
	    let annotate;
	    const blocks = (0, altiumBlockParser_1.parseBlocks)(contents);
	    for (const block of blocks) {
	        switch (block.blockName) {
	            case 'Design': {
	                const design = block.blockContents;
	                design.HierarchyMode = altiumProjectFileTypes_1.HierarchyModeSchema.parse(design.HierarchyMode);
	                design.PowerPortNamesTakePriority = altiumProjectFileTypes_1.PowerPortNamesTakePriorityModeSchema.parse(design.PowerPortNamesTakePriority);
	                design.NameNetsHierarchically = altiumProjectFileTypes_1.NameNetsHierarchicallyModeSchema.parse(design.NameNetsHierarchically);
	                design.ChannelRoomNamingStyle = altiumProjectFileTypes_1.ChannelRoomNamingStyleSchema.parse(design.ChannelRoomNamingStyle);
	                design.AnnotateSortOrder = parseAnnotateSortOrder(design.AnnotateSortOrder);
	                design.AnnotateProcessLocation = parseAnnotateProcessLocation(design.AnnotateProcessLocation);
	                project.Design = design;
	                break;
	            }
	            case 'Annotate':
	                annotate = block.blockContents;
	                break;
	            case 'Preferences':
	                project.Preferences = block.blockContents;
	                break;
	            case 'GeneratedDocument':
	                project.GeneratedDocument.push(block.blockContents);
	                break;
	            case 'Configuration':
	                project.Configuration.push(block.blockContents);
	                break;
	            case 'Document': {
	                const doc = block.blockContents;
	                const docIndexStr = /^Document(\d+)$/.exec(doc.id)?.[1];
	                doc._DocumentIndex = docIndexStr ? parseInt(docIndexStr, 10) : 0;
	                project.Document.push(doc);
	                break;
	            }
	            case 'Parameter': {
	                const param = block.blockContents;
	                const paramIndexStr = /^Parameter(\d+)$/.exec(param.id)?.[1];
	                param._Index = paramIndexStr ? parseInt(paramIndexStr, 10) : 0;
	                project.Parameter.push(param);
	                break;
	            }
	            case 'ProjectVariant':
	                project.ProjectVariant = project.ProjectVariant ?? [];
	                project.ProjectVariant.push(parseProjectVariant(block.blockContents));
	                break;
	            case 'DiffPairSuffix':
	                project.DiffPairSuffix ??= [];
	                project.DiffPairSuffix.push(AltiumDiffPairSuffixSchema.parse(block.blockContents));
	                break;
	        }
	    }
	    if (annotate) {
	        if ('SortOrder' in annotate) {
	            project.Design.AnnotateSortOrder = parseAnnotateSortOrder(annotate['SortOrder']);
	        }
	        if ('SortLocation' in annotate) {
	            project.Design.AnnotateProcessLocation = parseAnnotateProcessLocation(annotate['SortLocation']);
	        }
	    }
	    return project;
	}
	
	return projectFileParser;
}

var PCAD = {};

var pcadNetlistParser = {};

var hasRequiredPcadNetlistParser;

function requirePcadNetlistParser () {
	if (hasRequiredPcadNetlistParser) return pcadNetlistParser;
	hasRequiredPcadNetlistParser = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY = exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY = exports.PCAD_RAW_CONNECTIONS_METADATA_KEY = exports.PCAD_NO_CONNECT_PLACEHOLDER_NET_NAME = void 0;
		exports.getPcadNetlistRawConnections = getPcadNetlistRawConnections;
		exports.getPcadRejectedConnectionRows = getPcadRejectedConnectionRows;
		exports.usesPcadPositiveAssertionsOnly = usesPcadPositiveAssertionsOnly;
		exports.parsePcadNetlist = parsePcadNetlist;
		exports.PCAD_NO_CONNECT_PLACEHOLDER_NET_NAME = '?';
		exports.PCAD_RAW_CONNECTIONS_METADATA_KEY = 'pcad.rawConnections';
		exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY = 'pcad.rejectedConnectionRows';
		exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY = 'pcad.positiveAssertionsOnly';
		function isPcadRejectedConnectionRow(row) {
		    if (typeof row !== 'object' || row === null) {
		        return false;
		    }
		    const candidate = row;
		    return typeof candidate.lineNumber === 'number';
		}
		function isPcadNetlistRawConnection(connection) {
		    if (typeof connection !== 'object' || connection === null) {
		        return false;
		    }
		    const candidate = connection;
		    return (typeof candidate.moduleName === 'string' &&
		        typeof candidate.pinRef === 'string' &&
		        typeof candidate.netName === 'string');
		}
		function getPcadNetlistRawConnections(schematic) {
		    const serialized = schematic.metadata?.[exports.PCAD_RAW_CONNECTIONS_METADATA_KEY];
		    if (typeof serialized !== 'string') {
		        return undefined;
		    }
		    try {
		        const parsed = JSON.parse(serialized);
		        if (!Array.isArray(parsed) || !parsed.every((connection) => isPcadNetlistRawConnection(connection))) {
		            return undefined;
		        }
		        return parsed;
		    }
		    catch {
		        return undefined;
		    }
		}
		function getPcadRejectedConnectionRows(schematic) {
		    const serialized = schematic.metadata?.[exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY];
		    if (typeof serialized !== 'string') {
		        return undefined;
		    }
		    try {
		        const parsed = JSON.parse(serialized);
		        if (!Array.isArray(parsed) || !parsed.every((row) => isPcadRejectedConnectionRow(row))) {
		            return undefined;
		        }
		        return parsed;
		    }
		    catch {
		        return undefined;
		    }
		}
		function usesPcadPositiveAssertionsOnly(schematic) {
		    return schematic.metadata?.[exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY] === 'true';
		}
		function parsePcadNetlist(netlistContent, designId = 'default') {
		    const warnings = [];
		    const rejectedConnectionRows = [];
		    const components = [];
		    const parsedComponents = parsePcadComponents(netlistContent, warnings, rejectedConnectionRows);
		    components.push(...parsedComponents);
		    const modules = components.map((component) => convertToModule(component));
		    const nets = buildNetsFromComponents(components);
		    const rawConnections = components.flatMap((component) => component.pinConnections.map(({ pinRef, netName }) => ({
		        moduleName: component.moduleName,
		        pinRef,
		        netName,
		    })));
		    const positiveAssertionsOnly = /^\s*\{COMPONENT\s+ORCAD\.PCB(?:\s|$)/i.test(netlistContent);
		    const schematic = {
		        modelType: 'schematic',
		        designId,
		        sourceFile: 'pcad-netlist',
		        sheetIndex: 0,
		        layerSet: 'default',
		        drawings: {},
		        edges_bbox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
		        initial_zoombox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
		        nets,
		        modules,
		        ports: {},
		        metadata: {
		            title: 'PCAD Netlist Import',
		            revision: '',
		            company: '',
		            date: '',
		            [exports.PCAD_RAW_CONNECTIONS_METADATA_KEY]: JSON.stringify(rawConnections),
		            [exports.PCAD_REJECTED_CONNECTION_ROWS_METADATA_KEY]: JSON.stringify(rejectedConnectionRows),
		            ...(positiveAssertionsOnly ? { [exports.PCAD_POSITIVE_ASSERTIONS_ONLY_METADATA_KEY]: 'true' } : {}),
		        },
		        warnings,
		    };
		    return { schematic, rawConnections, warnings, rejectedConnectionRows };
		}
		const PCAD_PIN_FIELD = /^[^,\s]+(?:\s*,\s*[^,\s]+)*$/;
		function readPcadRowAlternatives(rawLine) {
		    const line = rawLine.trimEnd();
		    const readings = [];
		    for (let column = 0; column < line.length; column++) {
		        if (/\s/.test(line[column]) || (column > 0 && !/\s/.test(line[column - 1]))) {
		            continue;
		        }
		        const pinField = line.slice(0, column).trim();
		        if (pinField.length > 0 && !PCAD_PIN_FIELD.test(pinField)) {
		            continue;
		        }
		        readings.push({
		            column,
		            pinRef: pinField.replace(/\s*,\s*/g, ', '),
		            netName: line.slice(column),
		            overflows: pinField.length > 0 && !/\s/.test(line[0]),
		        });
		    }
		    return readings;
		}
		function readingFitsColumn(reading, column) {
		    return reading.column === column || (reading.overflows && reading.column > column);
		}
		function inferPcadNetNameColumns(blocks, warnings) {
		    const rows = blocks.flatMap((block) => block.cnRows.map(({ readings }) => readings));
		    const candidates = new Set(rows.flatMap((readings) => readings.map(({ column }) => column)));
		    const columns = [...candidates].filter((column) => rows.every((readings) => readings.some((reading) => readingFitsColumn(reading, column))));
		    if (columns.length === 1 || rows.length === 0) {
		        return columns;
		    }
		    if (columns.length > 1) {
		        const expressibleColumns = columns.filter((column) => blocks.every((block) => block.cnRows.filter(({ readings }) => {
		            const matchingReadings = readings.filter((reading) => readingFitsColumn(reading, column));
		            return matchingReadings.length === 1 && matchingReadings[0].pinRef === '';
		        }).length < 2));
		        if (expressibleColumns.length === 1) {
		            return expressibleColumns;
		        }
		    }
		    warnings.push(columns.length === 0 ? 'pcad-layout-conflict' : 'pcad-layout-unanchored');
		    return [];
		}
		function scanPcadComponentBlocks(lines) {
		    const blocks = [];
		    let inSubcomp = false;
		    let inComponent = false;
		    let inCnSection = false;
		    let currentBlock = null;
		    let braceDepth = 0;
		    for (let i = 0; i < lines.length; i++) {
		        const rawLine = lines[i];
		        const line = rawLine.trim();
		        if (!line || line.startsWith('//') || line.startsWith('#')) {
		            continue;
		        }
		        if (line.includes('{SUBCOMP')) {
		            inSubcomp = true;
		            continue;
		        }
		        if (!inSubcomp) {
		            continue;
		        }
		        const componentMatch = line.match(/^\{I\s+(.*?)\.PRT\s*(.*)$/);
		        if (componentMatch) {
		            if (currentBlock && currentBlock.moduleName) {
		                blocks.push(currentBlock);
		            }
		            currentBlock = {
		                footprintName: componentMatch[1].trim(),
		                moduleName: componentMatch[2].trim(),
		                cnRows: [],
		            };
		            inComponent = true;
		            braceDepth = 1;
		            continue;
		        }
		        if (inComponent) {
		            if (line === '{CN') {
		                inCnSection = true;
		                braceDepth++;
		                continue;
		            }
		            if (line === '}') {
		                braceDepth--;
		                if (inCnSection && braceDepth === 1) {
		                    inCnSection = false;
		                }
		                else if (braceDepth === 0) {
		                    if (currentBlock && currentBlock.moduleName) {
		                        blocks.push(currentBlock);
		                    }
		                    currentBlock = null;
		                    inComponent = false;
		                    inCnSection = false;
		                }
		                continue;
		            }
		            if (inCnSection && currentBlock) {
		                currentBlock.cnRows.push({ lineNumber: i + 1, rawLine, readings: readPcadRowAlternatives(rawLine) });
		            }
		        }
		        if (line === '}' && inSubcomp && !inComponent) {
		            inSubcomp = false;
		            continue;
		        }
		    }
		    if (currentBlock && currentBlock.moduleName) {
		        blocks.push(currentBlock);
		    }
		    return blocks;
		}
		function interpretPcadComponentBlock(block, netNameColumns, warnings, rejectedConnectionRows) {
		    const component = {
		        footprintName: block.footprintName,
		        moduleName: block.moduleName,
		        pinNets: {},
		        pinConnections: [],
		    };
		    for (const { lineNumber, rawLine, readings: rowReadings } of block.cnRows) {
		        const readings = rowReadings.filter((reading) => netNameColumns.some((column) => readingFitsColumn(reading, column)));
		        const resolvedReading = readings.length === 1 ? readings[0] : undefined;
		        if (resolvedReading === undefined) {
		            warnings.push(`pcad-row-ambiguous at line ${lineNumber}: ${rawLine.trim()}`);
		            rejectedConnectionRows.push({ lineNumber });
		            continue;
		        }
		        if (resolvedReading.pinRef === '') {
		            if (component.pinNets[''] !== undefined) {
		                warnings.push(`Component ${block.moduleName} declares more than one blank-pin connection row; rejecting the row at line ${lineNumber}`);
		                rejectedConnectionRows.push({ lineNumber });
		                continue;
		            }
		            component.pinConnections.push({ pinRef: '', netName: resolvedReading.netName });
		            component.pinNets[''] = resolvedReading.netName;
		            continue;
		        }
		        {
		            const pinPart = resolvedReading.pinRef;
		            const netName = resolvedReading.netName;
		            if (component.pinNets[pinPart] !== undefined) {
		                warnings.push(`Component ${block.moduleName} pin ${pinPart} appears in multiple nets; retaining ${netName} in pinNets`);
		            }
		            component.pinConnections.push({ pinRef: pinPart, netName });
		            component.pinNets[pinPart] = netName;
		            continue;
		        }
		    }
		    return component;
		}
		function parsePcadComponents(content, warnings, rejectedConnectionRows) {
		    const blocks = scanPcadComponentBlocks(content.split('\n'));
		    const columns = inferPcadNetNameColumns(blocks, warnings);
		    return blocks.map((block) => interpretPcadComponentBlock(block, columns, warnings, rejectedConnectionRows));
		}
		function convertToModule(component, _warnings) {
		    const transform = {
		        angle: 0,
		        mirrored: [false, false],
		    };
		    return {
		        ref: component.moduleName,
		        footprintRef: component.footprintName,
		        componentRef: component.footprintName,
		        center: [0, 0],
		        layer: 'F',
		        transform,
		        name: component.moduleName,
		        pinNets: component.pinNets,
		        drawProperties: {},
		    };
		}
		function buildNetsFromComponents(components) {
		    const nets = {};
		    for (const component of components) {
		        for (const [pinRef, netName] of Object.entries(component.pinNets)) {
		            if (!nets[netName]) {
		                nets[netName] = {
		                    name: netName,
		                    connections: [],
		                };
		            }
		            const connection = {
		                moduleRef: component.moduleName,
		                moduleName: component.moduleName,
		                pinRef,
		            };
		            nets[netName].connections.push(connection);
		        }
		    }
		    return nets;
		}
		
	} (pcadNetlistParser));
	return pcadNetlistParser;
}

var pcadValidation = {};

var hasRequiredPcadValidation;

function requirePcadValidation () {
	if (hasRequiredPcadValidation) return pcadValidation;
	hasRequiredPcadValidation = 1;
	Object.defineProperty(pcadValidation, "__esModule", { value: true });
	pcadValidation.getPcadFileType = getPcadFileType;
	pcadValidation.validatePcadFile = validatePcadFile;
	const PCAD_NETLIST_PREAMBLES = ['{COMPONENT PROTEL.PCB', '{COMPONENT', 'PROTEL.PCB'];
	function getPcadFileType(content) {
	    const trimmedContent = content.trim();
	    for (const preamble of PCAD_NETLIST_PREAMBLES) {
	        if (trimmedContent.startsWith(preamble)) {
	            return 'pcad-netlist';
	        }
	    }
	    const lines = trimmedContent.split('\n').slice(0, 10);
	    for (const line of lines) {
	        const trimmedLine = line.trim();
	        if (trimmedLine.includes('{COMPONENT') && trimmedLine.includes('PROTEL')) {
	            return 'pcad-netlist';
	        }
	        if (trimmedLine.includes('{SUBCOMP') || trimmedLine.includes('{DETAIL')) {
	            return 'pcad-netlist';
	        }
	    }
	    return undefined;
	}
	function validatePcadFile(_filename, content) {
	    try {
	        const fileType = getPcadFileType(content);
	        if (!fileType) {
	            return {
	                isValid: false,
	                errors: ['File does not appear to be a valid PCAD format file'],
	            };
	        }
	        const metadata = extractPcadMetadata(content, fileType);
	        const referencedFiles = extractReferencedFiles(content);
	        const data = {
	            semanticType: 'netlist',
	            fileFormat: {
	                cadTool: 'altium',
	                version: metadata.version || 'unknown',
	                type: fileType,
	            },
	            referencedFiles,
	            metadata,
	        };
	        return {
	            isValid: true,
	            data,
	        };
	    }
	    catch (error) {
	        return {
	            isValid: false,
	            errors: [`Validation failed: ${error instanceof Error ? error.message : String(error)}`],
	        };
	    }
	}
	function extractPcadMetadata(content, fileType) {
	    const metadata = {
	        fileType,
	        tool: 'PCAD/PROTEL',
	    };
	    const componentMatches = content.match(/\{I\s+.+?\.PRT\s+.+/g);
	    if (componentMatches) {
	        metadata.componentCount = componentMatches.length;
	    }
	    const envMatch = content.match(/\{ENVIRONMENT\s+(.+?)\}/);
	    if (envMatch) {
	        metadata.environment = envMatch[1].trim();
	    }
	    const versionMatch = content.match(/version\s*[:\s]\s*([0-9.]+)/i);
	    if (versionMatch) {
	        metadata.version = versionMatch[1];
	    }
	    return metadata;
	}
	function extractReferencedFiles(content) {
	    const referencedFiles = [];
	    const footprintMatches = content.match(/([^{\s]+)\.PRT/g);
	    if (footprintMatches) {
	        footprintMatches.forEach((match) => {
	            const filename = match.trim();
	            if (!referencedFiles.includes(filename)) {
	                referencedFiles.push(filename);
	            }
	        });
	    }
	    return referencedFiles;
	}
	
	return pcadValidation;
}

var hasRequiredPCAD;

function requirePCAD () {
	if (hasRequiredPCAD) return PCAD;
	hasRequiredPCAD = 1;
	(function (exports) {
		var __createBinding = (PCAD && PCAD.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (PCAD && PCAD.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePcadNetlistParser(), exports);
		__exportStar(requirePcadValidation(), exports);
		
	} (PCAD));
	return PCAD;
}

var altiumHarness = {};

var hasRequiredAltiumHarness;

function requireAltiumHarness () {
	if (hasRequiredAltiumHarness) return altiumHarness;
	hasRequiredAltiumHarness = 1;
	Object.defineProperty(altiumHarness, "__esModule", { value: true });
	altiumHarness.parseAltiumHarnessFile = parseAltiumHarnessFile;
	altiumHarness.validateAltiumHarnessFile = validateAltiumHarnessFile;
	const Util_1 = requireUtil();
	const altiumValidation_1 = requireAltiumValidation();
	const AltiumHarnessRegex = /^([^=\n]+)=(([^,\n]+)(,([^,\n]+))*)?$/;
	function parseAltiumHarnessFile(contents) {
	    const lines = contents.split('\n');
	    const harnesses = [];
	    for (let line of lines) {
	        line = line.trim();
	        if (line.length === 0) {
	            continue;
	        }
	        const match = AltiumHarnessRegex.exec(line);
	        if (!match) {
	            continue;
	        }
	        const name = match[1].trim();
	        const signalsString = match[2] || '';
	        const signals = signalsString.length > 0 ? signalsString.split(',').map((s) => s.trim()) : [];
	        harnesses.push({
	            name,
	            signals,
	        });
	    }
	    return harnesses;
	}
	async function validateAltiumHarnessFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.harness')) {
	        return undefined;
	    }
	    const textContents = (await (0, Util_1.getBlobTextClean)(fileContents)).trim();
	    const lines = textContents.split('\n');
	    for (const line of lines) {
	        const trimmedLine = line.trim();
	        if (trimmedLine.length === 0) {
	            continue;
	        }
	        if (!AltiumHarnessRegex.test(trimmedLine)) {
	            return undefined;
	        }
	    }
	    return {
	        referencedFiles: [],
	        metadata: {
	            [altiumValidation_1.AltiumProjectMetadataKey.FileType]: 'Altium Harness',
	            [altiumValidation_1.AltiumProjectMetadataKey.Harness]: parseAltiumHarnessFile(textContents),
	        },
	    };
	}
	
	return altiumHarness;
}

var altiumAnnotation = {};

var hasRequiredAltiumAnnotation;

function requireAltiumAnnotation () {
	if (hasRequiredAltiumAnnotation) return altiumAnnotation;
	hasRequiredAltiumAnnotation = 1;
	Object.defineProperty(altiumAnnotation, "__esModule", { value: true });
	altiumAnnotation.parseAltiumAnnotationFile = parseAltiumAnnotationFile;
	altiumAnnotation.validateAltiumAnnotationFile = validateAltiumAnnotationFile;
	const zod_1 = /*@__PURE__*/ requireZod();
	const altiumValidation_1 = requireAltiumValidation();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	const altiumBlockParser_1 = requireAltiumBlockParser();
	const AltiumAnnotationKeyRegex = /^([a-zA-Z]+)(\d+)/;
	const recordIndexSchema = zod_1.default.union([zod_1.default.number(), zod_1.default.literal(Infinity)]);
	const IAltiumAnnotationSchema = zod_1.default.object({
	    LogicalDesignator: zod_1.default.string(),
	    LogicalPartID: zod_1.default.string(),
	    DocumentName: zod_1.default.string(),
	    ChannelName: zod_1.default.string(),
	    UniqueID: zod_1.default.string(),
	    PhysicalDesignator: zod_1.default.string(),
	    PhysicalDesignatorLocked: zod_1.default.string(),
	    SchematicPartUniqueId: zod_1.default.string(),
	    _Index: recordIndexSchema,
	    MultiPartId: zod_1.default.boolean().optional(),
	});
	const IAltiumSheetNumberManagerRecordSchema = zod_1.default.object({
	    SheetNumberOrder: zod_1.default.string(),
	    SheetNumberMethod: zod_1.default.string(),
	    SheetNumbers: zod_1.default.array(zod_1.default.object({
	        DocumentName: zod_1.default.string(),
	        UniqueIDPath: zod_1.default.string(),
	        SheetNumber: zod_1.default.number().int().positive(),
	        _Index: zod_1.default.number().int().nonnegative(),
	    })),
	    Diagnostics: zod_1.default.array(zod_1.default.string()),
	    InvalidSheetNumbers: zod_1.default
	        .array(zod_1.default.object({ DocumentName: zod_1.default.string().optional(), UniqueIDPath: zod_1.default.string().optional(), _Index: recordIndexSchema }))
	        .optional(),
	});
	const IAltiumSheetNumberRecordSchema = zod_1.default.object({
	    DocumentName: zod_1.default
	        .string()
	        .min(1)
	        .refine((value) => value === value.trim()),
	    UniqueIDPath: zod_1.default.string().refine((value) => value === value.trim()),
	    SheetNumber: zod_1.default.number().int().positive(),
	    _Index: zod_1.default.number().int().nonnegative(),
	});
	function parseSheetNumberManager(blockContents, duplicateKeys) {
	    const records = new Map();
	    const indexedKeyRegex = /^(DocumentName|UniqueIDPath|SheetNumber)(\d+)$/;
	    const duplicateIndexes = new Set(duplicateKeys.flatMap((key) => {
	        const match = indexedKeyRegex.exec(key);
	        return match === null ? [] : [Number(match[2])];
	    }));
	    for (const [key, value] of Object.entries(blockContents)) {
	        const match = indexedKeyRegex.exec(key);
	        if (match === null) {
	            continue;
	        }
	        const fieldName = match[1];
	        const index = Number(match[2]);
	        const record = records.get(index) ?? { _Index: index };
	        records.set(index, record);
	        switch (fieldName) {
	            case 'DocumentName':
	                record.DocumentName = String(value);
	                break;
	            case 'UniqueIDPath':
	                record.UniqueIDPath = String(value);
	                break;
	            case 'SheetNumber': {
	                const sheetNumber = /^\d+$/.test(String(value)) ? Number(value) : undefined;
	                record.SheetNumber = sheetNumber !== undefined && Number.isSafeInteger(sheetNumber) ? sheetNumber : undefined;
	                break;
	            }
	        }
	    }
	    const sheetNumbers = [];
	    const invalidSheetNumbers = [];
	    const diagnostics = duplicateKeys.map((key) => `SheetNumberManager repeats field ${key}`);
	    for (const [index, record] of records) {
	        const parsedRecord = IAltiumSheetNumberRecordSchema.safeParse(record);
	        if (duplicateIndexes.has(index) || !parsedRecord.success) {
	            invalidSheetNumbers.push({
	                _Index: index,
	                DocumentName: duplicateKeys.includes(`DocumentName${index}`) ? undefined : record.DocumentName,
	                UniqueIDPath: duplicateKeys.includes(`UniqueIDPath${index}`) ? undefined : record.UniqueIDPath,
	            });
	            if (!duplicateIndexes.has(index)) {
	                diagnostics.push(`SheetNumberManager entry ${index} is incomplete or invalid`);
	            }
	            continue;
	        }
	        sheetNumbers.push(parsedRecord.data);
	    }
	    return IAltiumSheetNumberManagerRecordSchema.parse({
	        SheetNumberOrder: String(blockContents.SheetNumberOrder ?? ''),
	        SheetNumberMethod: String(blockContents.SheetNumberMethod ?? ''),
	        SheetNumbers: sheetNumbers,
	        ...(invalidSheetNumbers.length > 0 ? { InvalidSheetNumbers: invalidSheetNumbers } : {}),
	        Diagnostics: diagnostics,
	    });
	}
	function parseAnnotationsFromBlock(blockContents) {
	    const annotationMap = new Map();
	    for (const [key, value] of Object.entries(blockContents)) {
	        const match = AltiumAnnotationKeyRegex.exec(key);
	        if (!match) {
	            continue;
	        }
	        const [_, fieldName, indexStr] = match;
	        const index = parseInt(indexStr, 10);
	        if (!annotationMap.has(index)) {
	            annotationMap.set(index, { _Index: index });
	        }
	        const annotation = annotationMap.get(index);
	        annotation[fieldName] = String(value);
	    }
	    const annotations = [];
	    for (const [index, annotation] of annotationMap) {
	        if (annotation.UniqueID) {
	            annotation.SchematicPartUniqueId = annotation.UniqueID.substring(annotation.UniqueID.lastIndexOf('\\') + 1);
	        }
	        IAltiumAnnotationSchema.parse(annotation);
	        annotations[index] = annotation;
	    }
	    return annotations;
	}
	function parseAltiumAnnotationFile(contents) {
	    const blocks = (0, altiumBlockParser_1.parseBlocks)(contents, ['SheetNumberManager']);
	    const result = {
	        DesignatorManager: [],
	    };
	    let foundDesignatorManager = false;
	    for (const block of blocks) {
	        switch (block.blockName) {
	            case 'DesignatorManager':
	                foundDesignatorManager = true;
	                result.DesignatorManager = parseAnnotationsFromBlock(block.blockContents);
	                break;
	            case 'SheetNumberManager':
	                if (result.SheetNumberManager !== undefined) {
	                    result.SheetNumberManager.SheetNumbers = [];
	                    result.SheetNumberManager.Diagnostics.push('Multiple SheetNumberManager blocks are present');
	                }
	                else {
	                    result.SheetNumberManager = parseSheetNumberManager(block.blockContents, block.duplicateKeys ?? []);
	                }
	                break;
	        }
	    }
	    if (!foundDesignatorManager && contents.trim() !== '') {
	        throw new Error('Expected [DesignatorManager] section in annotation file');
	    }
	    return result;
	}
	async function validateAltiumAnnotationFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase())) {
	        return undefined;
	    }
	    const textContents = await fileContents.text();
	    const parsed = parseAltiumAnnotationFile(textContents);
	    return {
	        referencedFiles: [],
	        metadata: {
	            [altiumValidation_1.AltiumProjectMetadataKey.FileType]: 'Altium Annotation',
	            [altiumValidation_1.AltiumProjectMetadataKey.Annotation]: parsed,
	        },
	    };
	}
	
	return altiumAnnotation;
}

var SchDocParser = {};

var StreamParser = {};

var cfbInterop = {};

var cfb = {exports: {}};

/* cfb.js (C) 2013-present SheetJS -- http://sheetjs.com */
cfb.exports;

var hasRequiredCfb;

function requireCfb () {
	if (hasRequiredCfb) return cfb.exports;
	hasRequiredCfb = 1;
	(function (module) {
		/* vim: set ts=2: */
		/*jshint eqnull:true */
		/*exported CFB */
		/*global module, require:false, process:false, Buffer:false, Uint8Array:false, Uint16Array:false */

		var Base64_map = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";
		function Base64_encode(input) {
			var o = "";
			var c1 = 0, c2 = 0, c3 = 0, e1 = 0, e2 = 0, e3 = 0, e4 = 0;
			for (var i = 0; i < input.length; ) {
				c1 = input.charCodeAt(i++);
				e1 = (c1 >> 2);
				c2 = input.charCodeAt(i++);
				e2 = ((c1 & 3) << 4) | (c2 >> 4);
				c3 = input.charCodeAt(i++);
				e3 = ((c2 & 15) << 2) | (c3 >> 6);
				e4 = (c3 & 63);
				if (isNaN(c2)) e3 = e4 = 64;
				else if (isNaN(c3)) e4 = 64;
				o += Base64_map.charAt(e1) + Base64_map.charAt(e2) + Base64_map.charAt(e3) + Base64_map.charAt(e4);
			}
			return o;
		}
		function Base64_decode(input) {
			var o = "";
			var c1 = 0, c2 = 0, c3 = 0, e1 = 0, e2 = 0, e3 = 0, e4 = 0;
			input = input.replace(/[^\w\+\/\=]/g, "");
			for (var i = 0; i < input.length;) {
				e1 = Base64_map.indexOf(input.charAt(i++));
				e2 = Base64_map.indexOf(input.charAt(i++));
				c1 = (e1 << 2) | (e2 >> 4);
				o += String.fromCharCode(c1);
				e3 = Base64_map.indexOf(input.charAt(i++));
				c2 = ((e2 & 15) << 4) | (e3 >> 2);
				if (e3 !== 64) o += String.fromCharCode(c2);
				e4 = Base64_map.indexOf(input.charAt(i++));
				c3 = ((e3 & 3) << 6) | e4;
				if (e4 !== 64) o += String.fromCharCode(c3);
			}
			return o;
		}
		var has_buf = (function() { return typeof Buffer !== 'undefined' && typeof process !== 'undefined' && typeof process.versions !== 'undefined' && !!process.versions.node; })();

		var Buffer_from = (function() {
			if(typeof Buffer !== 'undefined') {
				var nbfs = !Buffer.from;
				if(!nbfs) try { Buffer.from("foo", "utf8"); } catch(e) { nbfs = true; }
				return nbfs ? function(buf, enc) { return (enc) ? new Buffer(buf, enc) : new Buffer(buf); } : Buffer.from.bind(Buffer);
			}
			return function() {};
		})();


		function new_raw_buf(len) {
			/* jshint -W056 */
			if(has_buf) {
				if(Buffer.alloc) return Buffer.alloc(len);
				var b = new Buffer(len); b.fill(0); return b;
			}
			return typeof Uint8Array != "undefined" ? new Uint8Array(len) : new Array(len);
			/* jshint +W056 */
		}

		function new_unsafe_buf(len) {
			/* jshint -W056 */
			if(has_buf) return Buffer.allocUnsafe ? Buffer.allocUnsafe(len) : new Buffer(len);
			return typeof Uint8Array != "undefined" ? new Uint8Array(len) : new Array(len);
			/* jshint +W056 */
		}

		var s2a = function s2a(s) {
			if(has_buf) return Buffer_from(s, "binary");
			return s.split("").map(function(x){ return x.charCodeAt(0) & 0xff; });
		};

		var chr0 = /\u0000/g, chr1 = /[\u0001-\u0006]/g;
		var __toBuffer = function(bufs) { var x = []; for(var i = 0; i < bufs[0].length; ++i) { x.push.apply(x, bufs[0][i]); } return x; };
		var ___toBuffer = __toBuffer;
		var __utf16le = function(b,s,e) { var ss=[]; for(var i=s; i<e; i+=2) ss.push(String.fromCharCode(__readUInt16LE(b,i))); return ss.join("").replace(chr0,''); };
		var ___utf16le = __utf16le;
		var __hexlify = function(b,s,l) { var ss=[]; for(var i=s; i<s+l; ++i) ss.push(("0" + b[i].toString(16)).slice(-2)); return ss.join(""); };
		var ___hexlify = __hexlify;
		var __bconcat = function(bufs) {
			if(Array.isArray(bufs[0])) return [].concat.apply([], bufs);
			var maxlen = 0, i = 0;
			for(i = 0; i < bufs.length; ++i) maxlen += bufs[i].length;
			var o = new Uint8Array(maxlen);
			for(i = 0, maxlen = 0; i < bufs.length; maxlen += bufs[i].length, ++i) o.set(bufs[i], maxlen);
			return o;
		};
		var bconcat = __bconcat;


		if(has_buf) {
			__utf16le = function(b,s,e) {
				if(!Buffer.isBuffer(b)) return ___utf16le(b,s,e);
				return b.toString('utf16le',s,e).replace(chr0,'')/*.replace(chr1,'!')*/;
			};
			__hexlify = function(b,s,l) { return Buffer.isBuffer(b) ? b.toString('hex',s,s+l) : ___hexlify(b,s,l); };
			__toBuffer = function(bufs) { return (bufs[0].length > 0 && Buffer.isBuffer(bufs[0][0])) ? Buffer.concat((bufs[0])) : ___toBuffer(bufs);};
			s2a = function(s) { return Buffer_from(s, "binary"); };
			bconcat = function(bufs) { return Buffer.isBuffer(bufs[0]) ? Buffer.concat(bufs) : __bconcat(bufs); };
		}


		var __readUInt8 = function(b, idx) { return b[idx]; };
		var __readUInt16LE = function(b, idx) { return b[idx+1]*(1<<8)+b[idx]; };
		var __readInt16LE = function(b, idx) { var u = b[idx+1]*(1<<8)+b[idx]; return (u < 0x8000) ? u : (0xffff - u + 1) * -1; };
		var __readUInt32LE = function(b, idx) { return b[idx+3]*(1<<24)+(b[idx+2]<<16)+(b[idx+1]<<8)+b[idx]; };
		var __readInt32LE = function(b, idx) { return (b[idx+3]<<24)+(b[idx+2]<<16)+(b[idx+1]<<8)+b[idx]; };

		function ReadShift(size, t) {
			var oI, oS, type = 0;
			switch(size) {
				case 1: oI = __readUInt8(this, this.l); break;
				case 2: oI = (t !== 'i' ? __readUInt16LE : __readInt16LE)(this, this.l); break;
				case 4: oI = __readInt32LE(this, this.l); break;
				case 16: type = 2; oS = __hexlify(this, this.l, size);
			}
			this.l += size; if(type === 0) return oI; return oS;
		}

		var __writeUInt32LE = function(b, val, idx) { b[idx] = (val & 0xFF); b[idx+1] = ((val >>> 8) & 0xFF); b[idx+2] = ((val >>> 16) & 0xFF); b[idx+3] = ((val >>> 24) & 0xFF); };
		var __writeInt32LE  = function(b, val, idx) { b[idx] = (val & 0xFF); b[idx+1] = ((val >> 8) & 0xFF); b[idx+2] = ((val >> 16) & 0xFF); b[idx+3] = ((val >> 24) & 0xFF); };

		function WriteShift(t, val, f) {
			var size = 0, i = 0;
			switch(f) {
				case "hex": for(; i < t; ++i) {
		this[this.l++] = parseInt(val.slice(2*i, 2*i+2), 16)||0;
				} return this;
				case "utf16le":
		var end = this.l + t;
					for(i = 0; i < Math.min(val.length, t); ++i) {
						var cc = val.charCodeAt(i);
						this[this.l++] = cc & 0xff;
						this[this.l++] = cc >> 8;
					}
					while(this.l < end) this[this.l++] = 0;
					return this;
			}
		switch(t) {
				case  1: size = 1; this[this.l] = val&0xFF; break;
				case  2: size = 2; this[this.l] = val&0xFF; val >>>= 8; this[this.l+1] = val&0xFF; break;
				case  4: size = 4; __writeUInt32LE(this, val, this.l); break;
				case -4: size = 4; __writeInt32LE(this, val, this.l); break;
			}
			this.l += size; return this;
		}

		function CheckField(hexstr, fld) {
			var m = __hexlify(this,this.l,hexstr.length>>1);
			if(m !== hexstr) throw new Error(fld + 'Expected ' + hexstr + ' saw ' + m);
			this.l += hexstr.length>>1;
		}

		function prep_blob(blob, pos) {
			blob.l = pos;
			blob.read_shift = ReadShift;
			blob.chk = CheckField;
			blob.write_shift = WriteShift;
		}

		function new_buf(sz) {
			var o = (new_raw_buf(sz));
			prep_blob(o, 0);
			return o;
		}

		/*! crc32.js (C) 2014-present SheetJS -- http://sheetjs.com */
		/* vim: set ts=2: */
		/*exported CRC32 */
		var CRC32 = (function() {
		var CRC32 = {};
		CRC32.version = '1.2.1';
		/*global Int32Array */
		function signed_crc_table() {
			var c = 0, table = new Array(256);

			for(var n =0; n != 256; ++n){
				c = n;
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				c = ((c&1) ? (-306674912 ^ (c >>> 1)) : (c >>> 1));
				table[n] = c;
			}

			return typeof Int32Array !== 'undefined' ? new Int32Array(table) : table;
		}

		var T0 = signed_crc_table();
		function slice_by_16_tables(T) {
			var c = 0, v = 0, n = 0, table = typeof Int32Array !== 'undefined' ? new Int32Array(4096) : new Array(4096) ;

			for(n = 0; n != 256; ++n) table[n] = T[n];
			for(n = 0; n != 256; ++n) {
				v = T[n];
				for(c = 256 + n; c < 4096; c += 256) v = table[c] = (v >>> 8) ^ T[v & 0xFF];
			}
			var out = [];
			for(n = 1; n != 16; ++n) out[n - 1] = typeof Int32Array !== 'undefined' ? table.subarray(n * 256, n * 256 + 256) : table.slice(n * 256, n * 256 + 256);
			return out;
		}
		var TT = slice_by_16_tables(T0);
		var T1 = TT[0],  T2 = TT[1],  T3 = TT[2],  T4 = TT[3],  T5 = TT[4];
		var T6 = TT[5],  T7 = TT[6],  T8 = TT[7],  T9 = TT[8],  Ta = TT[9];
		var Tb = TT[10], Tc = TT[11], Td = TT[12], Te = TT[13], Tf = TT[14];
		function crc32_bstr(bstr, seed) {
			var C = seed ^ -1;
			for(var i = 0, L = bstr.length; i < L;) C = (C>>>8) ^ T0[(C^bstr.charCodeAt(i++))&0xFF];
			return ~C;
		}

		function crc32_buf(B, seed) {
			var C = seed ^ -1, L = B.length - 15, i = 0;
			for(; i < L;) C =
				Tf[B[i++] ^ (C & 255)] ^
				Te[B[i++] ^ ((C >> 8) & 255)] ^
				Td[B[i++] ^ ((C >> 16) & 255)] ^
				Tc[B[i++] ^ (C >>> 24)] ^
				Tb[B[i++]] ^ Ta[B[i++]] ^ T9[B[i++]] ^ T8[B[i++]] ^
				T7[B[i++]] ^ T6[B[i++]] ^ T5[B[i++]] ^ T4[B[i++]] ^
				T3[B[i++]] ^ T2[B[i++]] ^ T1[B[i++]] ^ T0[B[i++]];
			L += 15;
			while(i < L) C = (C>>>8) ^ T0[(C^B[i++])&0xFF];
			return ~C;
		}

		function crc32_str(str, seed) {
			var C = seed ^ -1;
			for(var i = 0, L = str.length, c = 0, d = 0; i < L;) {
				c = str.charCodeAt(i++);
				if(c < 0x80) {
					C = (C>>>8) ^ T0[(C^c)&0xFF];
				} else if(c < 0x800) {
					C = (C>>>8) ^ T0[(C ^ (192|((c>>6)&31)))&0xFF];
					C = (C>>>8) ^ T0[(C ^ (128|(c&63)))&0xFF];
				} else if(c >= 0xD800 && c < 0xE000) {
					c = (c&1023)+64; d = str.charCodeAt(i++)&1023;
					C = (C>>>8) ^ T0[(C ^ (240|((c>>8)&7)))&0xFF];
					C = (C>>>8) ^ T0[(C ^ (128|((c>>2)&63)))&0xFF];
					C = (C>>>8) ^ T0[(C ^ (128|((d>>6)&15)|((c&3)<<4)))&0xFF];
					C = (C>>>8) ^ T0[(C ^ (128|(d&63)))&0xFF];
				} else {
					C = (C>>>8) ^ T0[(C ^ (224|((c>>12)&15)))&0xFF];
					C = (C>>>8) ^ T0[(C ^ (128|((c>>6)&63)))&0xFF];
					C = (C>>>8) ^ T0[(C ^ (128|(c&63)))&0xFF];
				}
			}
			return ~C;
		}
		CRC32.table = T0;
		CRC32.bstr = crc32_bstr;
		CRC32.buf = crc32_buf;
		CRC32.str = crc32_str;
		return CRC32;
		})();
		/* [MS-CFB] v20171201 */
		var CFB = (function _CFB(){
		var exports = {};
		exports.version = '1.2.2';
		/* [MS-CFB] 2.6.4 */
		function namecmp(l, r) {
			var L = l.split("/"), R = r.split("/");
			for(var i = 0, c = 0, Z = Math.min(L.length, R.length); i < Z; ++i) {
				if((c = L[i].length - R[i].length)) return c;
				if(L[i] != R[i]) return L[i] < R[i] ? -1 : 1;
			}
			return L.length - R.length;
		}
		function dirname(p) {
			if(p.charAt(p.length - 1) == "/") return (p.slice(0,-1).indexOf("/") === -1) ? p : dirname(p.slice(0, -1));
			var c = p.lastIndexOf("/");
			return (c === -1) ? p : p.slice(0, c+1);
		}

		function filename(p) {
			if(p.charAt(p.length - 1) == "/") return filename(p.slice(0, -1));
			var c = p.lastIndexOf("/");
			return (c === -1) ? p : p.slice(c+1);
		}
		/* -------------------------------------------------------------------------- */
		/* DOS Date format:
		   high|YYYYYYYm.mmmddddd.HHHHHMMM.MMMSSSSS|low
		   add 1980 to stored year
		   stored second should be doubled
		*/

		/* write JS date to buf as a DOS date */
		function write_dos_date(buf, date) {
			if(typeof date === "string") date = new Date(date);
			var hms = date.getHours();
			hms = hms << 6 | date.getMinutes();
			hms = hms << 5 | (date.getSeconds()>>>1);
			buf.write_shift(2, hms);
			var ymd = (date.getFullYear() - 1980);
			ymd = ymd << 4 | (date.getMonth()+1);
			ymd = ymd << 5 | date.getDate();
			buf.write_shift(2, ymd);
		}

		/* read four bytes from buf and interpret as a DOS date */
		function parse_dos_date(buf) {
			var hms = buf.read_shift(2) & 0xFFFF;
			var ymd = buf.read_shift(2) & 0xFFFF;
			var val = new Date();
			var d = ymd & 0x1F; ymd >>>= 5;
			var m = ymd & 0x0F; ymd >>>= 4;
			val.setMilliseconds(0);
			val.setFullYear(ymd + 1980);
			val.setMonth(m-1);
			val.setDate(d);
			var S = hms & 0x1F; hms >>>= 5;
			var M = hms & 0x3F; hms >>>= 6;
			val.setHours(hms);
			val.setMinutes(M);
			val.setSeconds(S<<1);
			return val;
		}
		function parse_extra_field(blob) {
			prep_blob(blob, 0);
			var o = {};
			var flags = 0;
			while(blob.l <= blob.length - 4) {
				var type = blob.read_shift(2);
				var sz = blob.read_shift(2), tgt = blob.l + sz;
				var p = {};
				switch(type) {
					/* UNIX-style Timestamps */
					case 0x5455: {
						flags = blob.read_shift(1);
						if(flags & 1) p.mtime = blob.read_shift(4);
						/* for some reason, CD flag corresponds to LFH */
						if(sz > 5) {
							if(flags & 2) p.atime = blob.read_shift(4);
							if(flags & 4) p.ctime = blob.read_shift(4);
						}
						if(p.mtime) p.mt = new Date(p.mtime*1000);
					}
					break;
				}
				blob.l = tgt;
				o[type] = p;
			}
			return o;
		}
		var fs;
		function get_fs() { return fs || (fs = require$$0$2); }
		function parse(file, options) {
		if(file[0] == 0x50 && file[1] == 0x4b) return parse_zip(file, options);
		if((file[0] | 0x20) == 0x6d && (file[1]|0x20) == 0x69) return parse_mad(file, options);
		if(file.length < 512) throw new Error("CFB file size " + file.length + " < 512");
		var mver = 3;
		var ssz = 512;
		var nmfs = 0; // number of mini FAT sectors
		var difat_sec_cnt = 0;
		var dir_start = 0;
		var minifat_start = 0;
		var difat_start = 0;

		var fat_addrs = []; // locations of FAT sectors

		/* [MS-CFB] 2.2 Compound File Header */
		var blob = file.slice(0,512);
		prep_blob(blob, 0);

		/* major version */
		var mv = check_get_mver(blob);
		mver = mv[0];
		switch(mver) {
			case 3: ssz = 512; break; case 4: ssz = 4096; break;
			case 0: if(mv[1] == 0) return parse_zip(file, options);
			/* falls through */
			default: throw new Error("Major Version: Expected 3 or 4 saw " + mver);
		}

		/* reprocess header */
		if(ssz !== 512) { blob = file.slice(0,ssz); prep_blob(blob, 28 /* blob.l */); }
		/* Save header for final object */
		var header = file.slice(0,ssz);

		check_shifts(blob, mver);

		// Number of Directory Sectors
		var dir_cnt = blob.read_shift(4, 'i');
		if(mver === 3 && dir_cnt !== 0) throw new Error('# Directory Sectors: Expected 0 saw ' + dir_cnt);

		// Number of FAT Sectors
		blob.l += 4;

		// First Directory Sector Location
		dir_start = blob.read_shift(4, 'i');

		// Transaction Signature
		blob.l += 4;

		// Mini Stream Cutoff Size
		blob.chk('00100000', 'Mini Stream Cutoff Size: ');

		// First Mini FAT Sector Location
		minifat_start = blob.read_shift(4, 'i');

		// Number of Mini FAT Sectors
		nmfs = blob.read_shift(4, 'i');

		// First DIFAT sector location
		difat_start = blob.read_shift(4, 'i');

		// Number of DIFAT Sectors
		difat_sec_cnt = blob.read_shift(4, 'i');

		// Grab FAT Sector Locations
		for(var q = -1, j = 0; j < 109; ++j) { /* 109 = (512 - blob.l)>>>2; */
			q = blob.read_shift(4, 'i');
			if(q<0) break;
			fat_addrs[j] = q;
		}

		/** Break the file up into sectors */
		var sectors = sectorify(file, ssz);

		sleuth_fat(difat_start, difat_sec_cnt, sectors, ssz, fat_addrs);

		/** Chains */
		var sector_list = make_sector_list(sectors, dir_start, fat_addrs, ssz);

		sector_list[dir_start].name = "!Directory";
		if(nmfs > 0 && minifat_start !== ENDOFCHAIN) sector_list[minifat_start].name = "!MiniFAT";
		sector_list[fat_addrs[0]].name = "!FAT";
		sector_list.fat_addrs = fat_addrs;
		sector_list.ssz = ssz;

		/* [MS-CFB] 2.6.1 Compound File Directory Entry */
		var files = {}, Paths = [], FileIndex = [], FullPaths = [];
		read_directory(dir_start, sector_list, sectors, Paths, nmfs, files, FileIndex, minifat_start);

		build_full_paths(FileIndex, FullPaths, Paths);
		Paths.shift();

		var o = {
			FileIndex: FileIndex,
			FullPaths: FullPaths
		};

		// $FlowIgnore
		if(options && options.raw) o.raw = {header: header, sectors: sectors};
		return o;
		} // parse

		/* [MS-CFB] 2.2 Compound File Header -- read up to major version */
		function check_get_mver(blob) {
			if(blob[blob.l] == 0x50 && blob[blob.l + 1] == 0x4b) return [0, 0];
			// header signature 8
			blob.chk(HEADER_SIGNATURE, 'Header Signature: ');

			// clsid 16
			//blob.chk(HEADER_CLSID, 'CLSID: ');
			blob.l += 16;

			// minor version 2
			var mver = blob.read_shift(2, 'u');

			return [blob.read_shift(2,'u'), mver];
		}
		function check_shifts(blob, mver) {
			var shift = 0x09;

			// Byte Order
			//blob.chk('feff', 'Byte Order: '); // note: some writers put 0xffff
			blob.l += 2;

			// Sector Shift
			switch((shift = blob.read_shift(2))) {
				case 0x09: if(mver != 3) throw new Error('Sector Shift: Expected 9 saw ' + shift); break;
				case 0x0c: if(mver != 4) throw new Error('Sector Shift: Expected 12 saw ' + shift); break;
				default: throw new Error('Sector Shift: Expected 9 or 12 saw ' + shift);
			}

			// Mini Sector Shift
			blob.chk('0600', 'Mini Sector Shift: ');

			// Reserved
			blob.chk('000000000000', 'Reserved: ');
		}

		/** Break the file up into sectors */
		function sectorify(file, ssz) {
			var nsectors = Math.ceil(file.length/ssz)-1;
			var sectors = [];
			for(var i=1; i < nsectors; ++i) sectors[i-1] = file.slice(i*ssz,(i+1)*ssz);
			sectors[nsectors-1] = file.slice(nsectors*ssz);
			return sectors;
		}

		/* [MS-CFB] 2.6.4 Red-Black Tree */
		function build_full_paths(FI, FP, Paths) {
			var i = 0, L = 0, R = 0, C = 0, j = 0, pl = Paths.length;
			var dad = [], q = [];

			for(; i < pl; ++i) { dad[i]=q[i]=i; FP[i]=Paths[i]; }

			for(; j < q.length; ++j) {
				i = q[j];
				L = FI[i].L; R = FI[i].R; C = FI[i].C;
				if(dad[i] === i) {
					if(L !== -1 /*NOSTREAM*/ && dad[L] !== L) dad[i] = dad[L];
					if(R !== -1 && dad[R] !== R) dad[i] = dad[R];
				}
				if(C !== -1 /*NOSTREAM*/) dad[C] = i;
				if(L !== -1 && i != dad[i]) { dad[L] = dad[i]; if(q.lastIndexOf(L) < j) q.push(L); }
				if(R !== -1 && i != dad[i]) { dad[R] = dad[i]; if(q.lastIndexOf(R) < j) q.push(R); }
			}
			for(i=1; i < pl; ++i) if(dad[i] === i) {
				if(R !== -1 /*NOSTREAM*/ && dad[R] !== R) dad[i] = dad[R];
				else if(L !== -1 && dad[L] !== L) dad[i] = dad[L];
			}

			for(i=1; i < pl; ++i) {
				if(FI[i].type === 0 /* unknown */) continue;
				j = i;
				if(j != dad[j]) do {
					j = dad[j];
					FP[i] = FP[j] + "/" + FP[i];
				} while (j !== 0 && -1 !== dad[j] && j != dad[j]);
				dad[i] = -1;
			}

			FP[0] += "/";
			for(i=1; i < pl; ++i) {
				if(FI[i].type !== 2 /* stream */) FP[i] += "/";
			}
		}

		function get_mfat_entry(entry, payload, mini) {
			var start = entry.start, size = entry.size;
			//return (payload.slice(start*MSSZ, start*MSSZ + size));
			var o = [];
			var idx = start;
			while(mini && size > 0 && idx >= 0) {
				o.push(payload.slice(idx * MSSZ, idx * MSSZ + MSSZ));
				size -= MSSZ;
				idx = __readInt32LE(mini, idx * 4);
			}
			if(o.length === 0) return (new_buf(0));
			return (bconcat(o).slice(0, entry.size));
		}

		/** Chase down the rest of the DIFAT chain to build a comprehensive list
		    DIFAT chains by storing the next sector number as the last 32 bits */
		function sleuth_fat(idx, cnt, sectors, ssz, fat_addrs) {
			var q = ENDOFCHAIN;
			if(idx === ENDOFCHAIN) {
				if(cnt !== 0) throw new Error("DIFAT chain shorter than expected");
			} else if(idx !== -1 /*FREESECT*/) {
				var sector = sectors[idx], m = (ssz>>>2)-1;
				if(!sector) return;
				for(var i = 0; i < m; ++i) {
					if((q = __readInt32LE(sector,i*4)) === ENDOFCHAIN) break;
					fat_addrs.push(q);
				}
				if(cnt >= 1) sleuth_fat(__readInt32LE(sector,ssz-4),cnt - 1, sectors, ssz, fat_addrs);
			}
		}

		/** Follow the linked list of sectors for a given starting point */
		function get_sector_list(sectors, start, fat_addrs, ssz, chkd) {
			var buf = [], buf_chain = [];
			if(!chkd) chkd = [];
			var modulus = ssz - 1, j = 0, jj = 0;
			for(j=start; j>=0;) {
				chkd[j] = true;
				buf[buf.length] = j;
				buf_chain.push(sectors[j]);
				var addr = fat_addrs[Math.floor(j*4/ssz)];
				jj = ((j*4) & modulus);
				if(ssz < 4 + jj) throw new Error("FAT boundary crossed: " + j + " 4 "+ssz);
				if(!sectors[addr]) break;
				j = __readInt32LE(sectors[addr], jj);
			}
			return {nodes: buf, data:__toBuffer([buf_chain])};
		}

		/** Chase down the sector linked lists */
		function make_sector_list(sectors, dir_start, fat_addrs, ssz) {
			var sl = sectors.length, sector_list = ([]);
			var chkd = [], buf = [], buf_chain = [];
			var modulus = ssz - 1, i=0, j=0, k=0, jj=0;
			for(i=0; i < sl; ++i) {
				buf = ([]);
				k = (i + dir_start); if(k >= sl) k-=sl;
				if(chkd[k]) continue;
				buf_chain = [];
				var seen = [];
				for(j=k; j>=0;) {
					seen[j] = true;
					chkd[j] = true;
					buf[buf.length] = j;
					buf_chain.push(sectors[j]);
					var addr = fat_addrs[Math.floor(j*4/ssz)];
					jj = ((j*4) & modulus);
					if(ssz < 4 + jj) throw new Error("FAT boundary crossed: " + j + " 4 "+ssz);
					if(!sectors[addr]) break;
					j = __readInt32LE(sectors[addr], jj);
					if(seen[j]) break;
				}
				sector_list[k] = ({nodes: buf, data:__toBuffer([buf_chain])});
			}
			return sector_list;
		}

		/* [MS-CFB] 2.6.1 Compound File Directory Entry */
		function read_directory(dir_start, sector_list, sectors, Paths, nmfs, files, FileIndex, mini) {
			var minifat_store = 0, pl = (Paths.length?2:0);
			var sector = sector_list[dir_start].data;
			var i = 0, namelen = 0, name;
			for(; i < sector.length; i+= 128) {
				var blob = sector.slice(i, i+128);
				prep_blob(blob, 64);
				namelen = blob.read_shift(2);
				name = __utf16le(blob,0,namelen-pl);
				Paths.push(name);
				var o = ({
					name:  name,
					type:  blob.read_shift(1),
					color: blob.read_shift(1),
					L:     blob.read_shift(4, 'i'),
					R:     blob.read_shift(4, 'i'),
					C:     blob.read_shift(4, 'i'),
					clsid: blob.read_shift(16),
					state: blob.read_shift(4, 'i'),
					start: 0,
					size: 0
				});
				var ctime = blob.read_shift(2) + blob.read_shift(2) + blob.read_shift(2) + blob.read_shift(2);
				if(ctime !== 0) o.ct = read_date(blob, blob.l-8);
				var mtime = blob.read_shift(2) + blob.read_shift(2) + blob.read_shift(2) + blob.read_shift(2);
				if(mtime !== 0) o.mt = read_date(blob, blob.l-8);
				o.start = blob.read_shift(4, 'i');
				o.size = blob.read_shift(4, 'i');
				if(o.size < 0 && o.start < 0) { o.size = o.type = 0; o.start = ENDOFCHAIN; o.name = ""; }
				if(o.type === 5) { /* root */
					minifat_store = o.start;
					if(nmfs > 0 && minifat_store !== ENDOFCHAIN) sector_list[minifat_store].name = "!StreamData";
					/*minifat_size = o.size;*/
				} else if(o.size >= 4096 /* MSCSZ */) {
					o.storage = 'fat';
					if(sector_list[o.start] === undefined) sector_list[o.start] = get_sector_list(sectors, o.start, sector_list.fat_addrs, sector_list.ssz);
					sector_list[o.start].name = o.name;
					o.content = (sector_list[o.start].data.slice(0,o.size));
				} else {
					o.storage = 'minifat';
					if(o.size < 0) o.size = 0;
					else if(minifat_store !== ENDOFCHAIN && o.start !== ENDOFCHAIN && sector_list[minifat_store]) {
						o.content = get_mfat_entry(o, sector_list[minifat_store].data, (sector_list[mini]||{}).data);
					}
				}
				if(o.content) prep_blob(o.content, 0);
				files[name] = o;
				FileIndex.push(o);
			}
		}

		function read_date(blob, offset) {
			return new Date(( ( (__readUInt32LE(blob,offset+4)/1e7)*Math.pow(2,32)+__readUInt32LE(blob,offset)/1e7 ) - 11644473600)*1000);
		}

		function read_file(filename, options) {
			get_fs();
			return parse(fs.readFileSync(filename), options);
		}

		function read(blob, options) {
			var type = options && options.type;
			if(!type) {
				if(has_buf && Buffer.isBuffer(blob)) type = "buffer";
			}
			switch(type || "base64") {
				case "file": return read_file(blob, options);
				case "base64": return parse(s2a(Base64_decode(blob)), options);
				case "binary": return parse(s2a(blob), options);
			}
			return parse(blob, options);
		}

		function init_cfb(cfb, opts) {
			var o = opts || {}, root = o.root || "Root Entry";
			if(!cfb.FullPaths) cfb.FullPaths = [];
			if(!cfb.FileIndex) cfb.FileIndex = [];
			if(cfb.FullPaths.length !== cfb.FileIndex.length) throw new Error("inconsistent CFB structure");
			if(cfb.FullPaths.length === 0) {
				cfb.FullPaths[0] = root + "/";
				cfb.FileIndex[0] = ({ name: root, type: 5 });
			}
			if(o.CLSID) cfb.FileIndex[0].clsid = o.CLSID;
			seed_cfb(cfb);
		}
		function seed_cfb(cfb) {
			var nm = "\u0001Sh33tJ5";
			if(CFB.find(cfb, "/" + nm)) return;
			var p = new_buf(4); p[0] = 55; p[1] = p[3] = 50; p[2] = 54;
			cfb.FileIndex.push(({ name: nm, type: 2, content:p, size:4, L:69, R:69, C:69 }));
			cfb.FullPaths.push(cfb.FullPaths[0] + nm);
			rebuild_cfb(cfb);
		}
		function rebuild_cfb(cfb, f) {
			init_cfb(cfb);
			var gc = false, s = false;
			for(var i = cfb.FullPaths.length - 1; i >= 0; --i) {
				var _file = cfb.FileIndex[i];
				switch(_file.type) {
					case 0:
						if(s) gc = true;
						else { cfb.FileIndex.pop(); cfb.FullPaths.pop(); }
						break;
					case 1: case 2: case 5:
						s = true;
						if(isNaN(_file.R * _file.L * _file.C)) gc = true;
						if(_file.R > -1 && _file.L > -1 && _file.R == _file.L) gc = true;
						break;
					default: gc = true; break;
				}
			}
			if(!gc && !f) return;

			var now = new Date(1987, 1, 19), j = 0;
			// Track which names exist
			var fullPaths = Object.create ? Object.create(null) : {};
			var data = [];
			for(i = 0; i < cfb.FullPaths.length; ++i) {
				fullPaths[cfb.FullPaths[i]] = true;
				if(cfb.FileIndex[i].type === 0) continue;
				data.push([cfb.FullPaths[i], cfb.FileIndex[i]]);
			}
			for(i = 0; i < data.length; ++i) {
				var dad = dirname(data[i][0]);
				s = fullPaths[dad];
				while(!s) {
					while(dirname(dad) && !fullPaths[dirname(dad)]) dad = dirname(dad);

					data.push([dad, ({
						name: filename(dad).replace("/",""),
						type: 1,
						clsid: HEADER_CLSID,
						ct: now, mt: now,
						content: null
					})]);

					// Add name to set
					fullPaths[dad] = true;

					dad = dirname(data[i][0]);
					s = fullPaths[dad];
				}
			}

			data.sort(function(x,y) { return namecmp(x[0], y[0]); });
			cfb.FullPaths = []; cfb.FileIndex = [];
			for(i = 0; i < data.length; ++i) { cfb.FullPaths[i] = data[i][0]; cfb.FileIndex[i] = data[i][1]; }
			for(i = 0; i < data.length; ++i) {
				var elt = cfb.FileIndex[i];
				var nm = cfb.FullPaths[i];

				elt.name =  filename(nm).replace("/","");
				elt.L = elt.R = elt.C = -(elt.color = 1);
				elt.size = elt.content ? elt.content.length : 0;
				elt.start = 0;
				elt.clsid = (elt.clsid || HEADER_CLSID);
				if(i === 0) {
					elt.C = data.length > 1 ? 1 : -1;
					elt.size = 0;
					elt.type = 5;
				} else if(nm.slice(-1) == "/") {
					for(j=i+1;j < data.length; ++j) if(dirname(cfb.FullPaths[j])==nm) break;
					elt.C = j >= data.length ? -1 : j;
					for(j=i+1;j < data.length; ++j) if(dirname(cfb.FullPaths[j])==dirname(nm)) break;
					elt.R = j >= data.length ? -1 : j;
					elt.type = 1;
				} else {
					if(dirname(cfb.FullPaths[i+1]||"") == dirname(nm)) elt.R = i + 1;
					elt.type = 2;
				}
			}

		}

		function _write(cfb, options) {
			var _opts = options || {};
			/* MAD is order-sensitive, skip rebuild and sort */
			if(_opts.fileType == 'mad') return write_mad(cfb, _opts);
			rebuild_cfb(cfb);
			switch(_opts.fileType) {
				case 'zip': return write_zip(cfb, _opts);
				//case 'mad': return write_mad(cfb, _opts);
			}
			var L = (function(cfb){
				var mini_size = 0, fat_size = 0;
				for(var i = 0; i < cfb.FileIndex.length; ++i) {
					var file = cfb.FileIndex[i];
					if(!file.content) continue;
					var flen = file.content.length;
					if(flen > 0){
						if(flen < 0x1000) mini_size += (flen + 0x3F) >> 6;
						else fat_size += (flen + 0x01FF) >> 9;
					}
				}
				var dir_cnt = (cfb.FullPaths.length +3) >> 2;
				var mini_cnt = (mini_size + 7) >> 3;
				var mfat_cnt = (mini_size + 0x7F) >> 7;
				var fat_base = mini_cnt + fat_size + dir_cnt + mfat_cnt;
				var fat_cnt = (fat_base + 0x7F) >> 7;
				var difat_cnt = fat_cnt <= 109 ? 0 : Math.ceil((fat_cnt-109)/0x7F);
				while(((fat_base + fat_cnt + difat_cnt + 0x7F) >> 7) > fat_cnt) difat_cnt = ++fat_cnt <= 109 ? 0 : Math.ceil((fat_cnt-109)/0x7F);
				var L =  [1, difat_cnt, fat_cnt, mfat_cnt, dir_cnt, fat_size, mini_size, 0];
				cfb.FileIndex[0].size = mini_size << 6;
				L[7] = (cfb.FileIndex[0].start=L[0]+L[1]+L[2]+L[3]+L[4]+L[5])+((L[6]+7) >> 3);
				return L;
			})(cfb);
			var o = new_buf(L[7] << 9);
			var i = 0, T = 0;
			{
				for(i = 0; i < 8; ++i) o.write_shift(1, HEADER_SIG[i]);
				for(i = 0; i < 8; ++i) o.write_shift(2, 0);
				o.write_shift(2, 0x003E);
				o.write_shift(2, 0x0003);
				o.write_shift(2, 0xFFFE);
				o.write_shift(2, 0x0009);
				o.write_shift(2, 0x0006);
				for(i = 0; i < 3; ++i) o.write_shift(2, 0);
				o.write_shift(4, 0);
				o.write_shift(4, L[2]);
				o.write_shift(4, L[0] + L[1] + L[2] + L[3] - 1);
				o.write_shift(4, 0);
				o.write_shift(4, 1<<12);
				o.write_shift(4, L[3] ? L[0] + L[1] + L[2] - 1: ENDOFCHAIN);
				o.write_shift(4, L[3]);
				o.write_shift(-4, L[1] ? L[0] - 1: ENDOFCHAIN);
				o.write_shift(4, L[1]);
				for(i = 0; i < 109; ++i) o.write_shift(-4, i < L[2] ? L[1] + i : -1);
			}
			if(L[1]) {
				for(T = 0; T < L[1]; ++T) {
					for(; i < 236 + T * 127; ++i) o.write_shift(-4, i < L[2] ? L[1] + i : -1);
					o.write_shift(-4, T === L[1] - 1 ? ENDOFCHAIN : T + 1);
				}
			}
			var chainit = function(w) {
				for(T += w; i<T-1; ++i) o.write_shift(-4, i+1);
				if(w) { ++i; o.write_shift(-4, ENDOFCHAIN); }
			};
			T = i = 0;
			for(T+=L[1]; i<T; ++i) o.write_shift(-4, consts.DIFSECT);
			for(T+=L[2]; i<T; ++i) o.write_shift(-4, consts.FATSECT);
			chainit(L[3]);
			chainit(L[4]);
			var j = 0, flen = 0;
			var file = cfb.FileIndex[0];
			for(; j < cfb.FileIndex.length; ++j) {
				file = cfb.FileIndex[j];
				if(!file.content) continue;
		flen = file.content.length;
				if(flen < 0x1000) continue;
				file.start = T;
				chainit((flen + 0x01FF) >> 9);
			}
			chainit((L[6] + 7) >> 3);
			while(o.l & 0x1FF) o.write_shift(-4, consts.ENDOFCHAIN);
			T = i = 0;
			for(j = 0; j < cfb.FileIndex.length; ++j) {
				file = cfb.FileIndex[j];
				if(!file.content) continue;
		flen = file.content.length;
				if(!flen || flen >= 0x1000) continue;
				file.start = T;
				chainit((flen + 0x3F) >> 6);
			}
			while(o.l & 0x1FF) o.write_shift(-4, consts.ENDOFCHAIN);
			for(i = 0; i < L[4]<<2; ++i) {
				var nm = cfb.FullPaths[i];
				if(!nm || nm.length === 0) {
					for(j = 0; j < 17; ++j) o.write_shift(4, 0);
					for(j = 0; j < 3; ++j) o.write_shift(4, -1);
					for(j = 0; j < 12; ++j) o.write_shift(4, 0);
					continue;
				}
				file = cfb.FileIndex[i];
				if(i === 0) file.start = file.size ? file.start - 1 : ENDOFCHAIN;
				var _nm = (i === 0 && _opts.root) || file.name;
				if(_nm.length > 32) {
					console.error("Name " + _nm + " will be truncated to " + _nm.slice(0,32));
					_nm = _nm.slice(0, 32);
				}
				flen = 2*(_nm.length+1);
				o.write_shift(64, _nm, "utf16le");
				o.write_shift(2, flen);
				o.write_shift(1, file.type);
				o.write_shift(1, file.color);
				o.write_shift(-4, file.L);
				o.write_shift(-4, file.R);
				o.write_shift(-4, file.C);
				if(!file.clsid) for(j = 0; j < 4; ++j) o.write_shift(4, 0);
				else o.write_shift(16, file.clsid, "hex");
				o.write_shift(4, file.state || 0);
				o.write_shift(4, 0); o.write_shift(4, 0);
				o.write_shift(4, 0); o.write_shift(4, 0);
				o.write_shift(4, file.start);
				o.write_shift(4, file.size); o.write_shift(4, 0);
			}
			for(i = 1; i < cfb.FileIndex.length; ++i) {
				file = cfb.FileIndex[i];
		if(file.size >= 0x1000) {
					o.l = (file.start+1) << 9;
					if (has_buf && Buffer.isBuffer(file.content)) {
						file.content.copy(o, o.l, 0, file.size);
						// o is a 0-filled Buffer so just set next offset
						o.l += (file.size + 511) & -512;
					} else {
						for(j = 0; j < file.size; ++j) o.write_shift(1, file.content[j]);
						for(; j & 0x1FF; ++j) o.write_shift(1, 0);
					}
				}
			}
			for(i = 1; i < cfb.FileIndex.length; ++i) {
				file = cfb.FileIndex[i];
		if(file.size > 0 && file.size < 0x1000) {
					if (has_buf && Buffer.isBuffer(file.content)) {
						file.content.copy(o, o.l, 0, file.size);
						// o is a 0-filled Buffer so just set next offset
						o.l += (file.size + 63) & -64;
					} else {
						for(j = 0; j < file.size; ++j) o.write_shift(1, file.content[j]);
						for(; j & 0x3F; ++j) o.write_shift(1, 0);
					}
				}
			}
			if (has_buf) {
				o.l = o.length;
			} else {
				// When using Buffer, already 0-filled
				while(o.l < o.length) o.write_shift(1, 0);
			}
			return o;
		}
		/* [MS-CFB] 2.6.4 (Unicode 3.0.1 case conversion) */
		function find(cfb, path) {
			var UCFullPaths = cfb.FullPaths.map(function(x) { return x.toUpperCase(); });
			var UCPaths = UCFullPaths.map(function(x) { var y = x.split("/"); return y[y.length - (x.slice(-1) == "/" ? 2 : 1)]; });
			var k = false;
			if(path.charCodeAt(0) === 47 /* "/" */) { k = true; path = UCFullPaths[0].slice(0, -1) + path; }
			else k = path.indexOf("/") !== -1;
			var UCPath = path.toUpperCase();
			var w = k === true ? UCFullPaths.indexOf(UCPath) : UCPaths.indexOf(UCPath);
			if(w !== -1) return cfb.FileIndex[w];

			var m = !UCPath.match(chr1);
			UCPath = UCPath.replace(chr0,'');
			if(m) UCPath = UCPath.replace(chr1,'!');
			for(w = 0; w < UCFullPaths.length; ++w) {
				if((m ? UCFullPaths[w].replace(chr1,'!') : UCFullPaths[w]).replace(chr0,'') == UCPath) return cfb.FileIndex[w];
				if((m ? UCPaths[w].replace(chr1,'!') : UCPaths[w]).replace(chr0,'') == UCPath) return cfb.FileIndex[w];
			}
			return null;
		}
		/** CFB Constants */
		var MSSZ = 64; /* Mini Sector Size = 1<<6 */
		//var MSCSZ = 4096; /* Mini Stream Cutoff Size */
		/* 2.1 Compound File Sector Numbers and Types */
		var ENDOFCHAIN = -2;
		/* 2.2 Compound File Header */
		var HEADER_SIGNATURE = 'd0cf11e0a1b11ae1';
		var HEADER_SIG = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];
		var HEADER_CLSID = '00000000000000000000000000000000';
		var consts = {
			/* 2.1 Compund File Sector Numbers and Types */
			MAXREGSECT: -6,
			DIFSECT: -4,
			FATSECT: -3,
			ENDOFCHAIN: ENDOFCHAIN,
			FREESECT: -1,
			/* 2.2 Compound File Header */
			HEADER_SIGNATURE: HEADER_SIGNATURE,
			HEADER_MINOR_VERSION: '3e00',
			MAXREGSID: -6,
			NOSTREAM: -1,
			HEADER_CLSID: HEADER_CLSID,
			/* 2.6.1 Compound File Directory Entry */
			EntryTypes: ['unknown','storage','stream','lockbytes','property','root']
		};

		function write_file(cfb, filename, options) {
			get_fs();
			var o = _write(cfb, options);
		fs.writeFileSync(filename, o);
		}

		function a2s(o) {
			var out = new Array(o.length);
			for(var i = 0; i < o.length; ++i) out[i] = String.fromCharCode(o[i]);
			return out.join("");
		}

		function write(cfb, options) {
			var o = _write(cfb, options);
			switch(options && options.type || "buffer") {
				case "file": get_fs(); fs.writeFileSync(options.filename, (o)); return o;
				case "binary": return typeof o == "string" ? o : a2s(o);
				case "base64": return Base64_encode(typeof o == "string" ? o : a2s(o));
				case "buffer": if(has_buf) return Buffer.isBuffer(o) ? o : Buffer_from(o);
					/* falls through */
				case "array": return typeof o == "string" ? s2a(o) : o;
			}
			return o;
		}
		/* node < 8.1 zlib does not expose bytesRead, so default to pure JS */
		var _zlib;
		function use_zlib(zlib) { try {
			var InflateRaw = zlib.InflateRaw;
			var InflRaw = new InflateRaw();
			InflRaw._processChunk(new Uint8Array([3, 0]), InflRaw._finishFlushFlag);
			if(InflRaw.bytesRead) _zlib = zlib;
			else throw new Error("zlib does not expose bytesRead");
		} catch(e) {console.error("cannot use native zlib: " + (e.message || e)); } }

		function _inflateRawSync(payload, usz) {
			if(!_zlib) return _inflate(payload, usz);
			var InflateRaw = _zlib.InflateRaw;
			var InflRaw = new InflateRaw();
			var out = InflRaw._processChunk(payload.slice(payload.l), InflRaw._finishFlushFlag);
			payload.l += InflRaw.bytesRead;
			return out;
		}

		function _deflateRawSync(payload) {
			return _zlib ? _zlib.deflateRawSync(payload) : _deflate(payload);
		}
		var CLEN_ORDER = [ 16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15 ];

		/*  LEN_ID = [ 257, 258, 259, 260, 261, 262, 263, 264, 265, 266, 267, 268, 269, 270, 271, 272, 273, 274, 275, 276, 277, 278, 279, 280, 281, 282, 283, 284, 285 ]; */
		var LEN_LN = [   3,   4,   5,   6,   7,   8,   9,  10,  11,  13 , 15,  17,  19,  23,  27,  31,  35,  43,  51,  59,  67,  83,  99, 115, 131, 163, 195, 227, 258 ];

		/*  DST_ID = [  0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13,  14,  15,  16,  17,  18,  19,   20,   21,   22,   23,   24,   25,   26,    27,    28,    29 ]; */
		var DST_LN = [  1,  2,  3,  4,  5,  7,  9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577 ];

		function bit_swap_8(n) { var t = (((((n<<1)|(n<<11)) & 0x22110) | (((n<<5)|(n<<15)) & 0x88440))); return ((t>>16) | (t>>8) |t)&0xFF; }

		var use_typed_arrays = typeof Uint8Array !== 'undefined';

		var bitswap8 = use_typed_arrays ? new Uint8Array(1<<8) : [];
		for(var q = 0; q < (1<<8); ++q) bitswap8[q] = bit_swap_8(q);

		function bit_swap_n(n, b) {
			var rev = bitswap8[n & 0xFF];
			if(b <= 8) return rev >>> (8-b);
			rev = (rev << 8) | bitswap8[(n>>8)&0xFF];
			if(b <= 16) return rev >>> (16-b);
			rev = (rev << 8) | bitswap8[(n>>16)&0xFF];
			return rev >>> (24-b);
		}

		/* helpers for unaligned bit reads */
		function read_bits_2(buf, bl) { var w = (bl&7), h = (bl>>>3); return ((buf[h]|(w <= 6 ? 0 : buf[h+1]<<8))>>>w)& 0x03; }
		function read_bits_3(buf, bl) { var w = (bl&7), h = (bl>>>3); return ((buf[h]|(w <= 5 ? 0 : buf[h+1]<<8))>>>w)& 0x07; }
		function read_bits_4(buf, bl) { var w = (bl&7), h = (bl>>>3); return ((buf[h]|(w <= 4 ? 0 : buf[h+1]<<8))>>>w)& 0x0F; }
		function read_bits_5(buf, bl) { var w = (bl&7), h = (bl>>>3); return ((buf[h]|(w <= 3 ? 0 : buf[h+1]<<8))>>>w)& 0x1F; }
		function read_bits_7(buf, bl) { var w = (bl&7), h = (bl>>>3); return ((buf[h]|(w <= 1 ? 0 : buf[h+1]<<8))>>>w)& 0x7F; }

		/* works up to n = 3 * 8 + 1 = 25 */
		function read_bits_n(buf, bl, n) {
			var w = (bl&7), h = (bl>>>3), f = ((1<<n)-1);
			var v = buf[h] >>> w;
			if(n < 8 - w) return v & f;
			v |= buf[h+1]<<(8-w);
			if(n < 16 - w) return v & f;
			v |= buf[h+2]<<(16-w);
			if(n < 24 - w) return v & f;
			v |= buf[h+3]<<(24-w);
			return v & f;
		}

		/* helpers for unaligned bit writes */
		function write_bits_3(buf, bl, v) { var w = bl & 7, h = bl >>> 3;
			if(w <= 5) buf[h] |= (v & 7) << w;
			else {
				buf[h] |= (v << w) & 0xFF;
				buf[h+1] = (v&7) >> (8-w);
			}
			return bl + 3;
		}

		function write_bits_1(buf, bl, v) {
			var w = bl & 7, h = bl >>> 3;
			v = (v&1) << w;
			buf[h] |= v;
			return bl + 1;
		}
		function write_bits_8(buf, bl, v) {
			var w = bl & 7, h = bl >>> 3;
			v <<= w;
			buf[h] |=  v & 0xFF; v >>>= 8;
			buf[h+1] = v;
			return bl + 8;
		}
		function write_bits_16(buf, bl, v) {
			var w = bl & 7, h = bl >>> 3;
			v <<= w;
			buf[h] |=  v & 0xFF; v >>>= 8;
			buf[h+1] = v & 0xFF;
			buf[h+2] = v >>> 8;
			return bl + 16;
		}

		/* until ArrayBuffer#realloc is a thing, fake a realloc */
		function realloc(b, sz) {
			var L = b.length, M = 2*L > sz ? 2*L : sz + 5, i = 0;
			if(L >= sz) return b;
			if(has_buf) {
				var o = new_unsafe_buf(M);
				// $FlowIgnore
				if(b.copy) b.copy(o);
				else for(; i < b.length; ++i) o[i] = b[i];
				return o;
			} else if(use_typed_arrays) {
				var a = new Uint8Array(M);
				if(a.set) a.set(b);
				else for(; i < L; ++i) a[i] = b[i];
				return a;
			}
			b.length = M;
			return b;
		}

		/* zero-filled arrays for older browsers */
		function zero_fill_array(n) {
			var o = new Array(n);
			for(var i = 0; i < n; ++i) o[i] = 0;
			return o;
		}

		/* build tree (used for literals and lengths) */
		function build_tree(clens, cmap, MAX) {
			var maxlen = 1, w = 0, i = 0, j = 0, ccode = 0, L = clens.length;

			var bl_count  = use_typed_arrays ? new Uint16Array(32) : zero_fill_array(32);
			for(i = 0; i < 32; ++i) bl_count[i] = 0;

			for(i = L; i < MAX; ++i) clens[i] = 0;
			L = clens.length;

			var ctree = use_typed_arrays ? new Uint16Array(L) : zero_fill_array(L); // []

			/* build code tree */
			for(i = 0; i < L; ++i) {
				bl_count[(w = clens[i])]++;
				if(maxlen < w) maxlen = w;
				ctree[i] = 0;
			}
			bl_count[0] = 0;
			for(i = 1; i <= maxlen; ++i) bl_count[i+16] = (ccode = (ccode + bl_count[i-1])<<1);
			for(i = 0; i < L; ++i) {
				ccode = clens[i];
				if(ccode != 0) ctree[i] = bl_count[ccode+16]++;
			}

			/* cmap[maxlen + 4 bits] = (off&15) + (lit<<4) reverse mapping */
			var cleni = 0;
			for(i = 0; i < L; ++i) {
				cleni = clens[i];
				if(cleni != 0) {
					ccode = bit_swap_n(ctree[i], maxlen)>>(maxlen-cleni);
					for(j = (1<<(maxlen + 4 - cleni)) - 1; j>=0; --j)
						cmap[ccode|(j<<cleni)] = (cleni&15) | (i<<4);
				}
			}
			return maxlen;
		}

		/* Fixed Huffman */
		var fix_lmap = use_typed_arrays ? new Uint16Array(512) : zero_fill_array(512);
		var fix_dmap = use_typed_arrays ? new Uint16Array(32)  : zero_fill_array(32);
		if(!use_typed_arrays) {
			for(var i = 0; i < 512; ++i) fix_lmap[i] = 0;
			for(i = 0; i < 32; ++i) fix_dmap[i] = 0;
		}
		(function() {
			var dlens = [];
			var i = 0;
			for(;i<32; i++) dlens.push(5);
			build_tree(dlens, fix_dmap, 32);

			var clens = [];
			i = 0;
			for(; i<=143; i++) clens.push(8);
			for(; i<=255; i++) clens.push(9);
			for(; i<=279; i++) clens.push(7);
			for(; i<=287; i++) clens.push(8);
			build_tree(clens, fix_lmap, 288);
		})();var _deflateRaw = (function _deflateRawIIFE() {
			var DST_LN_RE = use_typed_arrays ? new Uint8Array(0x8000) : [];
			var j = 0, k = 0;
			for(; j < DST_LN.length - 1; ++j) {
				for(; k < DST_LN[j+1]; ++k) DST_LN_RE[k] = j;
			}
			for(;k < 32768; ++k) DST_LN_RE[k] = 29;

			var LEN_LN_RE = use_typed_arrays ? new Uint8Array(0x103) : [];
			for(j = 0, k = 0; j < LEN_LN.length - 1; ++j) {
				for(; k < LEN_LN[j+1]; ++k) LEN_LN_RE[k] = j;
			}

			function write_stored(data, out) {
				var boff = 0;
				while(boff < data.length) {
					var L = Math.min(0xFFFF, data.length - boff);
					var h = boff + L == data.length;
					out.write_shift(1, +h);
					out.write_shift(2, L);
					out.write_shift(2, (~L) & 0xFFFF);
					while(L-- > 0) out[out.l++] = data[boff++];
				}
				return out.l;
			}

			/* Fixed Huffman */
			function write_huff_fixed(data, out) {
				var bl = 0;
				var boff = 0;
				var addrs = use_typed_arrays ? new Uint16Array(0x8000) : [];
				while(boff < data.length) {
					var L = /* data.length - boff; */ Math.min(0xFFFF, data.length - boff);

					/* write a stored block for short data */
					if(L < 10) {
						bl = write_bits_3(out, bl, +!!(boff + L == data.length)); // jshint ignore:line
						if(bl & 7) bl += 8 - (bl & 7);
						out.l = (bl / 8) | 0;
						out.write_shift(2, L);
						out.write_shift(2, (~L) & 0xFFFF);
						while(L-- > 0) out[out.l++] = data[boff++];
						bl = out.l * 8;
						continue;
					}

					bl = write_bits_3(out, bl, +!!(boff + L == data.length) + 2); // jshint ignore:line
					var hash = 0;
					while(L-- > 0) {
						var d = data[boff];
						hash = ((hash << 5) ^ d) & 0x7FFF;

						var match = -1, mlen = 0;

						if((match = addrs[hash])) {
							match |= boff & ~0x7FFF;
							if(match > boff) match -= 0x8000;
							if(match < boff) while(data[match + mlen] == data[boff + mlen] && mlen < 250) ++mlen;
						}

						if(mlen > 2) {
							/* Copy Token  */
							d = LEN_LN_RE[mlen];
							if(d <= 22) bl = write_bits_8(out, bl, bitswap8[d+1]>>1) - 1;
							else {
								write_bits_8(out, bl, 3);
								bl += 5;
								write_bits_8(out, bl, bitswap8[d-23]>>5);
								bl += 3;
							}
							var len_eb = (d < 8) ? 0 : ((d - 4)>>2);
							if(len_eb > 0) {
								write_bits_16(out, bl, mlen - LEN_LN[d]);
								bl += len_eb;
							}

							d = DST_LN_RE[boff - match];
							bl = write_bits_8(out, bl, bitswap8[d]>>3);
							bl -= 3;

							var dst_eb = d < 4 ? 0 : (d-2)>>1;
							if(dst_eb > 0) {
								write_bits_16(out, bl, boff - match - DST_LN[d]);
								bl += dst_eb;
							}
							for(var q = 0; q < mlen; ++q) {
								addrs[hash] = boff & 0x7FFF;
								hash = ((hash << 5) ^ data[boff]) & 0x7FFF;
								++boff;
							}
							L-= mlen - 1;
						} else {
							/* Literal Token */
							if(d <= 143) d = d + 48;
							else bl = write_bits_1(out, bl, 1);
							bl = write_bits_8(out, bl, bitswap8[d]);
							addrs[hash] = boff & 0x7FFF;
							++boff;
						}
					}

					bl = write_bits_8(out, bl, 0) - 1;
				}
				out.l = ((bl + 7)/8)|0;
				return out.l;
			}
			return function _deflateRaw(data, out) {
				if(data.length < 8) return write_stored(data, out);
				return write_huff_fixed(data, out);
			};
		})();

		function _deflate(data) {
			var buf = new_buf(50+Math.floor(data.length*1.1));
			var off = _deflateRaw(data, buf);
			return buf.slice(0, off);
		}
		/* modified inflate function also moves original read head */

		var dyn_lmap = use_typed_arrays ? new Uint16Array(32768) : zero_fill_array(32768);
		var dyn_dmap = use_typed_arrays ? new Uint16Array(32768) : zero_fill_array(32768);
		var dyn_cmap = use_typed_arrays ? new Uint16Array(128)   : zero_fill_array(128);
		var dyn_len_1 = 1, dyn_len_2 = 1;

		/* 5.5.3 Expanding Huffman Codes */
		function dyn(data, boff) {
			/* nomenclature from RFC1951 refers to bit values; these are offset by the implicit constant */
			var _HLIT = read_bits_5(data, boff) + 257; boff += 5;
			var _HDIST = read_bits_5(data, boff) + 1; boff += 5;
			var _HCLEN = read_bits_4(data, boff) + 4; boff += 4;
			var w = 0;

			/* grab and store code lengths */
			var clens = use_typed_arrays ? new Uint8Array(19) : zero_fill_array(19);
			var ctree = [ 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 ];
			var maxlen = 1;
			var bl_count =  use_typed_arrays ? new Uint8Array(8) : zero_fill_array(8);
			var next_code = use_typed_arrays ? new Uint8Array(8) : zero_fill_array(8);
			var L = clens.length; /* 19 */
			for(var i = 0; i < _HCLEN; ++i) {
				clens[CLEN_ORDER[i]] = w = read_bits_3(data, boff);
				if(maxlen < w) maxlen = w;
				bl_count[w]++;
				boff += 3;
			}

			/* build code tree */
			var ccode = 0;
			bl_count[0] = 0;
			for(i = 1; i <= maxlen; ++i) next_code[i] = ccode = (ccode + bl_count[i-1])<<1;
			for(i = 0; i < L; ++i) if((ccode = clens[i]) != 0) ctree[i] = next_code[ccode]++;
			/* cmap[7 bits from stream] = (off&7) + (lit<<3) */
			var cleni = 0;
			for(i = 0; i < L; ++i) {
				cleni = clens[i];
				if(cleni != 0) {
					ccode = bitswap8[ctree[i]]>>(8-cleni);
					for(var j = (1<<(7-cleni))-1; j>=0; --j) dyn_cmap[ccode|(j<<cleni)] = (cleni&7) | (i<<3);
				}
			}

			/* read literal and dist codes at once */
			var hcodes = [];
			maxlen = 1;
			for(; hcodes.length < _HLIT + _HDIST;) {
				ccode = dyn_cmap[read_bits_7(data, boff)];
				boff += ccode & 7;
				switch((ccode >>>= 3)) {
					case 16:
						w = 3 + read_bits_2(data, boff); boff += 2;
						ccode = hcodes[hcodes.length - 1];
						while(w-- > 0) hcodes.push(ccode);
						break;
					case 17:
						w = 3 + read_bits_3(data, boff); boff += 3;
						while(w-- > 0) hcodes.push(0);
						break;
					case 18:
						w = 11 + read_bits_7(data, boff); boff += 7;
						while(w -- > 0) hcodes.push(0);
						break;
					default:
						hcodes.push(ccode);
						if(maxlen < ccode) maxlen = ccode;
						break;
				}
			}

			/* build literal / length trees */
			var h1 = hcodes.slice(0, _HLIT), h2 = hcodes.slice(_HLIT);
			for(i = _HLIT; i < 286; ++i) h1[i] = 0;
			for(i = _HDIST; i < 30; ++i) h2[i] = 0;
			dyn_len_1 = build_tree(h1, dyn_lmap, 286);
			dyn_len_2 = build_tree(h2, dyn_dmap, 30);
			return boff;
		}

		/* return [ data, bytesRead ] */
		function inflate(data, usz) {
			/* shortcircuit for empty buffer [0x03, 0x00] */
			if(data[0] == 3 && !(data[1] & 0x3)) { return [new_raw_buf(usz), 2]; }

			/* bit offset */
			var boff = 0;

			/* header includes final bit and type bits */
			var header = 0;

			var outbuf = new_unsafe_buf(usz ? usz : (1<<18));
			var woff = 0;
			var OL = outbuf.length>>>0;
			var max_len_1 = 0, max_len_2 = 0;

			while((header&1) == 0) {
				header = read_bits_3(data, boff); boff += 3;
				if((header >>> 1) == 0) {
					/* Stored block */
					if(boff & 7) boff += 8 - (boff&7);
					/* 2 bytes sz, 2 bytes bit inverse */
					var sz = data[boff>>>3] | data[(boff>>>3)+1]<<8;
					boff += 32;
					/* push sz bytes */
					if(sz > 0) {
						if(!usz && OL < woff + sz) { outbuf = realloc(outbuf, woff + sz); OL = outbuf.length; }
						while(sz-- > 0) { outbuf[woff++] = data[boff>>>3]; boff += 8; }
					}
					continue;
				} else if((header >> 1) == 1) {
					/* Fixed Huffman */
					max_len_1 = 9; max_len_2 = 5;
				} else {
					/* Dynamic Huffman */
					boff = dyn(data, boff);
					max_len_1 = dyn_len_1; max_len_2 = dyn_len_2;
				}
				for(;;) { // while(true) is apparently out of vogue in modern JS circles
					if(!usz && (OL < woff + 32767)) { outbuf = realloc(outbuf, woff + 32767); OL = outbuf.length; }
					/* ingest code and move read head */
					var bits = read_bits_n(data, boff, max_len_1);
					var code = (header>>>1) == 1 ? fix_lmap[bits] : dyn_lmap[bits];
					boff += code & 15; code >>>= 4;
					/* 0-255 are literals, 256 is end of block token, 257+ are copy tokens */
					if(((code>>>8)&0xFF) === 0) outbuf[woff++] = code;
					else if(code == 256) break;
					else {
						code -= 257;
						var len_eb = (code < 8) ? 0 : ((code-4)>>2); if(len_eb > 5) len_eb = 0;
						var tgt = woff + LEN_LN[code];
						/* length extra bits */
						if(len_eb > 0) {
							tgt += read_bits_n(data, boff, len_eb);
							boff += len_eb;
						}

						/* dist code */
						bits = read_bits_n(data, boff, max_len_2);
						code = (header>>>1) == 1 ? fix_dmap[bits] : dyn_dmap[bits];
						boff += code & 15; code >>>= 4;
						var dst_eb = (code < 4 ? 0 : (code-2)>>1);
						var dst = DST_LN[code];
						/* dist extra bits */
						if(dst_eb > 0) {
							dst += read_bits_n(data, boff, dst_eb);
							boff += dst_eb;
						}

						/* in the common case, manual byte copy is faster than TA set / Buffer copy */
						if(!usz && OL < tgt) { outbuf = realloc(outbuf, tgt + 100); OL = outbuf.length; }
						while(woff < tgt) { outbuf[woff] = outbuf[woff - dst]; ++woff; }
					}
				}
			}
			if(usz) return [outbuf, (boff+7)>>>3];
			return [outbuf.slice(0, woff), (boff+7)>>>3];
		}

		function _inflate(payload, usz) {
			var data = payload.slice(payload.l||0);
			var out = inflate(data, usz);
			payload.l += out[1];
			return out[0];
		}

		function warn_or_throw(wrn, msg) {
			if(wrn) { if(typeof console !== 'undefined') console.error(msg); }
			else throw new Error(msg);
		}

		function parse_zip(file, options) {
			var blob = file;
			prep_blob(blob, 0);

			var FileIndex = [], FullPaths = [];
			var o = {
				FileIndex: FileIndex,
				FullPaths: FullPaths
			};
			init_cfb(o, { root: options.root });

			/* find end of central directory, start just after signature */
			var i = blob.length - 4;
			while((blob[i] != 0x50 || blob[i+1] != 0x4b || blob[i+2] != 0x05 || blob[i+3] != 0x06) && i >= 0) --i;
			blob.l = i + 4;

			/* parse end of central directory */
			blob.l += 4;
			var fcnt = blob.read_shift(2);
			blob.l += 6;
			var start_cd = blob.read_shift(4);

			/* parse central directory */
			blob.l = start_cd;

			for(i = 0; i < fcnt; ++i) {
				/* trust local file header instead of CD entry */
				blob.l += 20;
				var csz = blob.read_shift(4);
				var usz = blob.read_shift(4);
				var namelen = blob.read_shift(2);
				var efsz = blob.read_shift(2);
				var fcsz = blob.read_shift(2);
				blob.l += 8;
				var offset = blob.read_shift(4);
				var EF = parse_extra_field(blob.slice(blob.l+namelen, blob.l+namelen+efsz));
				blob.l += namelen + efsz + fcsz;

				var L = blob.l;
				blob.l = offset + 4;
				parse_local_file(blob, csz, usz, o, EF);
				blob.l = L;
			}

			return o;
		}


		/* head starts just after local file header signature */
		function parse_local_file(blob, csz, usz, o, EF) {
			/* [local file header] */
			blob.l += 2;
			var flags = blob.read_shift(2);
			var meth = blob.read_shift(2);
			var date = parse_dos_date(blob);

			if(flags & 0x2041) throw new Error("Unsupported ZIP encryption");
			var crc32 = blob.read_shift(4);
			var _csz = blob.read_shift(4);
			var _usz = blob.read_shift(4);

			var namelen = blob.read_shift(2);
			var efsz = blob.read_shift(2);

			// TODO: flags & (1<<11) // UTF8
			var name = ""; for(var i = 0; i < namelen; ++i) name += String.fromCharCode(blob[blob.l++]);
			if(efsz) {
				var ef = parse_extra_field(blob.slice(blob.l, blob.l + efsz));
				if((ef[0x5455]||{}).mt) date = ef[0x5455].mt;
				if(((EF||{})[0x5455]||{}).mt) date = EF[0x5455].mt;
			}
			blob.l += efsz;

			/* [encryption header] */

			/* [file data] */
			var data = blob.slice(blob.l, blob.l + _csz);
			switch(meth) {
				case 8: data = _inflateRawSync(blob, _usz); break;
				case 0: break; // TODO: scan for magic number
				default: throw new Error("Unsupported ZIP Compression method " + meth);
			}

			/* [data descriptor] */
			var wrn = false;
			if(flags & 8) {
				crc32 = blob.read_shift(4);
				if(crc32 == 0x08074b50) { crc32 = blob.read_shift(4); wrn = true; }
				_csz = blob.read_shift(4);
				_usz = blob.read_shift(4);
			}

			if(_csz != csz) warn_or_throw(wrn, "Bad compressed size: " + csz + " != " + _csz);
			if(_usz != usz) warn_or_throw(wrn, "Bad uncompressed size: " + usz + " != " + _usz);
			var _crc32 = CRC32.buf(data, 0);
			if((crc32>>0) != (_crc32>>0)) warn_or_throw(wrn, "Bad CRC32 checksum: " + crc32 + " != " + _crc32);
			cfb_add(o, name, data, {unsafe: true, mt: date});
		}
		function write_zip(cfb, options) {
			var _opts = options || {};
			var out = [], cdirs = [];
			var o = new_buf(1);
			var method = (_opts.compression ? 8 : 0), flags = 0;
			var i = 0, j = 0;

			var start_cd = 0, fcnt = 0;
			var root = cfb.FullPaths[0], fp = root, fi = cfb.FileIndex[0];
			var crcs = [];
			var sz_cd = 0;

			for(i = 1; i < cfb.FullPaths.length; ++i) {
				fp = cfb.FullPaths[i].slice(root.length); fi = cfb.FileIndex[i];
				if(!fi.size || !fi.content || fp == "\u0001Sh33tJ5") continue;
				var start = start_cd;

				/* TODO: CP437 filename */
				var namebuf = new_buf(fp.length);
				for(j = 0; j < fp.length; ++j) namebuf.write_shift(1, fp.charCodeAt(j) & 0x7F);
				namebuf = namebuf.slice(0, namebuf.l);
				crcs[fcnt] = CRC32.buf(fi.content, 0);

				var outbuf = fi.content;
				if(method == 8) outbuf = _deflateRawSync(outbuf);

				/* local file header */
				o = new_buf(30);
				o.write_shift(4, 0x04034b50);
				o.write_shift(2, 20);
				o.write_shift(2, flags);
				o.write_shift(2, method);
				/* TODO: last mod file time/date */
				if(fi.mt) write_dos_date(o, fi.mt);
				else o.write_shift(4, 0);
				o.write_shift(-4, crcs[fcnt]);
				o.write_shift(4,  outbuf.length);
				o.write_shift(4,  fi.content.length);
				o.write_shift(2, namebuf.length);
				o.write_shift(2, 0);

				start_cd += o.length;
				out.push(o);
				start_cd += namebuf.length;
				out.push(namebuf);

				/* TODO: extra fields? */

				/* TODO: encryption header ? */

				start_cd += outbuf.length;
				out.push(outbuf);

				/* central directory */
				o = new_buf(46);
				o.write_shift(4, 0x02014b50);
				o.write_shift(2, 0);
				o.write_shift(2, 20);
				o.write_shift(2, flags);
				o.write_shift(2, method);
				o.write_shift(4, 0); /* TODO: last mod file time/date */
				o.write_shift(-4, crcs[fcnt]);

				o.write_shift(4, outbuf.length);
				o.write_shift(4, fi.content.length);
				o.write_shift(2, namebuf.length);
				o.write_shift(2, 0);
				o.write_shift(2, 0);
				o.write_shift(2, 0);
				o.write_shift(2, 0);
				o.write_shift(4, 0);
				o.write_shift(4, start);

				sz_cd += o.l;
				cdirs.push(o);
				sz_cd += namebuf.length;
				cdirs.push(namebuf);
				++fcnt;
			}

			/* end of central directory */
			o = new_buf(22);
			o.write_shift(4, 0x06054b50);
			o.write_shift(2, 0);
			o.write_shift(2, 0);
			o.write_shift(2, fcnt);
			o.write_shift(2, fcnt);
			o.write_shift(4, sz_cd);
			o.write_shift(4, start_cd);
			o.write_shift(2, 0);

			return bconcat(([bconcat((out)), bconcat(cdirs), o]));
		}
		var ContentTypeMap = ({
			"htm": "text/html",
			"xml": "text/xml",

			"gif": "image/gif",
			"jpg": "image/jpeg",
			"png": "image/png",

			"mso": "application/x-mso",
			"thmx": "application/vnd.ms-officetheme",
			"sh33tj5": "application/octet-stream"
		});

		function get_content_type(fi, fp) {
			if(fi.ctype) return fi.ctype;

			var ext = fi.name || "", m = ext.match(/\.([^\.]+)$/);
			if(m && ContentTypeMap[m[1]]) return ContentTypeMap[m[1]];

			if(fp) {
				m = (ext = fp).match(/[\.\\]([^\.\\])+$/);
				if(m && ContentTypeMap[m[1]]) return ContentTypeMap[m[1]];
			}

			return "application/octet-stream";
		}

		/* 76 character chunks TODO: intertwine encoding */
		function write_base64_76(bstr) {
			var data = Base64_encode(bstr);
			var o = [];
			for(var i = 0; i < data.length; i+= 76) o.push(data.slice(i, i+76));
			return o.join("\r\n") + "\r\n";
		}

		/*
		Rules for QP:
			- escape =## applies for all non-display characters and literal "="
			- space or tab at end of line must be encoded
			- \r\n newlines can be preserved, but bare \r and \n must be escaped
			- lines must not exceed 76 characters, use soft breaks =\r\n

		TODO: Some files from word appear to write line extensions with bare equals:

		```
		<table class=3DMsoTableGrid border=3D1 cellspacing=3D0 cellpadding=3D0 width=
		="70%"
		```
		*/
		function write_quoted_printable(text) {
			var encoded = text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7E-\xFF=]/g, function(c) {
				var w = c.charCodeAt(0).toString(16).toUpperCase();
				return "=" + (w.length == 1 ? "0" + w : w);
			});

			encoded = encoded.replace(/ $/mg, "=20").replace(/\t$/mg, "=09");

			if(encoded.charAt(0) == "\n") encoded = "=0D" + encoded.slice(1);
			encoded = encoded.replace(/\r(?!\n)/mg, "=0D").replace(/\n\n/mg, "\n=0A").replace(/([^\r\n])\n/mg, "$1=0A");

			var o = [], split = encoded.split("\r\n");
			for(var si = 0; si < split.length; ++si) {
				var str = split[si];
				if(str.length == 0) { o.push(""); continue; }
				for(var i = 0; i < str.length;) {
					var end = 76;
					var tmp = str.slice(i, i + end);
					if(tmp.charAt(end - 1) == "=") end --;
					else if(tmp.charAt(end - 2) == "=") end -= 2;
					else if(tmp.charAt(end - 3) == "=") end -= 3;
					tmp = str.slice(i, i + end);
					i += end;
					if(i < str.length) tmp += "=";
					o.push(tmp);
				}
			}

			return o.join("\r\n");
		}
		function parse_quoted_printable(data) {
			var o = [];

			/* unify long lines */
			for(var di = 0; di < data.length; ++di) {
				var line = data[di];
				while(di <= data.length && line.charAt(line.length - 1) == "=") line = line.slice(0, line.length - 1) + data[++di];
				o.push(line);
			}

			/* decode */
			for(var oi = 0; oi < o.length; ++oi) o[oi] = o[oi].replace(/[=][0-9A-Fa-f]{2}/g, function($$) { return String.fromCharCode(parseInt($$.slice(1), 16)); });
			return s2a(o.join("\r\n"));
		}


		function parse_mime(cfb, data, root) {
			var fname = "", cte = "", ctype = "", fdata;
			var di = 0;
			for(;di < 10; ++di) {
				var line = data[di];
				if(!line || line.match(/^\s*$/)) break;
				var m = line.match(/^(.*?):\s*([^\s].*)$/);
				if(m) switch(m[1].toLowerCase()) {
					case "content-location": fname = m[2].trim(); break;
					case "content-type": ctype = m[2].trim(); break;
					case "content-transfer-encoding": cte = m[2].trim(); break;
				}
			}
			++di;
			switch(cte.toLowerCase()) {
				case 'base64': fdata = s2a(Base64_decode(data.slice(di).join(""))); break;
				case 'quoted-printable': fdata = parse_quoted_printable(data.slice(di)); break;
				default: throw new Error("Unsupported Content-Transfer-Encoding " + cte);
			}
			var file = cfb_add(cfb, fname.slice(root.length), fdata, {unsafe: true});
			if(ctype) file.ctype = ctype;
		}

		function parse_mad(file, options) {
			if(a2s(file.slice(0,13)).toLowerCase() != "mime-version:") throw new Error("Unsupported MAD header");
			var root = (options && options.root || "");
			// $FlowIgnore
			var data = (has_buf && Buffer.isBuffer(file) ? file.toString("binary") : a2s(file)).split("\r\n");
			var di = 0, row = "";

			/* if root is not specified, scan for the common prefix */
			for(di = 0; di < data.length; ++di) {
				row = data[di];
				if(!/^Content-Location:/i.test(row)) continue;
				row = row.slice(row.indexOf("file"));
				if(!root) root = row.slice(0, row.lastIndexOf("/") + 1);
				if(row.slice(0, root.length) == root) continue;
				while(root.length > 0) {
					root = root.slice(0, root.length - 1);
					root = root.slice(0, root.lastIndexOf("/") + 1);
					if(row.slice(0,root.length) == root) break;
				}
			}

			var mboundary = (data[1] || "").match(/boundary="(.*?)"/);
			if(!mboundary) throw new Error("MAD cannot find boundary");
			var boundary = "--" + (mboundary[1] || "");

			var FileIndex = [], FullPaths = [];
			var o = {
				FileIndex: FileIndex,
				FullPaths: FullPaths
			};
			init_cfb(o);
			var start_di, fcnt = 0;
			for(di = 0; di < data.length; ++di) {
				var line = data[di];
				if(line !== boundary && line !== boundary + "--") continue;
				if(fcnt++) parse_mime(o, data.slice(start_di, di), root);
				start_di = di;
			}
			return o;
		}

		function write_mad(cfb, options) {
			var opts = options || {};
			var boundary = opts.boundary || "SheetJS";
			boundary = '------=' + boundary;

			var out = [
				'MIME-Version: 1.0',
				'Content-Type: multipart/related; boundary="' + boundary.slice(2) + '"',
				'',
				'',
				''
			];

			var root = cfb.FullPaths[0], fp = root, fi = cfb.FileIndex[0];
			for(var i = 1; i < cfb.FullPaths.length; ++i) {
				fp = cfb.FullPaths[i].slice(root.length);
				fi = cfb.FileIndex[i];
				if(!fi.size || !fi.content || fp == "\u0001Sh33tJ5") continue;

				/* Normalize filename */
				fp = fp.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7E-\xFF]/g, function(c) {
					return "_x" + c.charCodeAt(0).toString(16) + "_";
				}).replace(/[\u0080-\uFFFF]/g, function(u) {
					return "_u" + u.charCodeAt(0).toString(16) + "_";
				});

				/* Extract content as binary string */
				var ca = fi.content;
				// $FlowIgnore
				var cstr = has_buf && Buffer.isBuffer(ca) ? ca.toString("binary") : a2s(ca);

				/* 4/5 of first 1024 chars ascii -> quoted printable, else base64 */
				var dispcnt = 0, L = Math.min(1024, cstr.length), cc = 0;
				for(var csl = 0; csl <= L; ++csl) if((cc=cstr.charCodeAt(csl)) >= 0x20 && cc < 0x80) ++dispcnt;
				var qp = dispcnt >= L * 4 / 5;

				out.push(boundary);
				out.push('Content-Location: ' + (opts.root || 'file:///C:/SheetJS/') + fp);
				out.push('Content-Transfer-Encoding: ' + (qp ? 'quoted-printable' : 'base64'));
				out.push('Content-Type: ' + get_content_type(fi, fp));
				out.push('');

				out.push(qp ? write_quoted_printable(cstr) : write_base64_76(cstr));
			}
			out.push(boundary + '--\r\n');
			return out.join("\r\n");
		}
		function cfb_new(opts) {
			var o = ({});
			init_cfb(o, opts);
			return o;
		}

		function cfb_add(cfb, name, content, opts) {
			var unsafe = opts && opts.unsafe;
			if(!unsafe) init_cfb(cfb);
			var file = !unsafe && CFB.find(cfb, name);
			if(!file) {
				var fpath = cfb.FullPaths[0];
				if(name.slice(0, fpath.length) == fpath) fpath = name;
				else {
					if(fpath.slice(-1) != "/") fpath += "/";
					fpath = (fpath + name).replace("//","/");
				}
				file = ({name: filename(name), type: 2});
				cfb.FileIndex.push(file);
				cfb.FullPaths.push(fpath);
				if(!unsafe) CFB.utils.cfb_gc(cfb);
			}
		file.content = (content);
			file.size = content ? content.length : 0;
			if(opts) {
				if(opts.CLSID) file.clsid = opts.CLSID;
				if(opts.mt) file.mt = opts.mt;
				if(opts.ct) file.ct = opts.ct;
			}
			return file;
		}

		function cfb_del(cfb, name) {
			init_cfb(cfb);
			var file = CFB.find(cfb, name);
			if(file) for(var j = 0; j < cfb.FileIndex.length; ++j) if(cfb.FileIndex[j] == file) {
				cfb.FileIndex.splice(j, 1);
				cfb.FullPaths.splice(j, 1);
				return true;
			}
			return false;
		}

		function cfb_mov(cfb, old_name, new_name) {
			init_cfb(cfb);
			var file = CFB.find(cfb, old_name);
			if(file) for(var j = 0; j < cfb.FileIndex.length; ++j) if(cfb.FileIndex[j] == file) {
				cfb.FileIndex[j].name = filename(new_name);
				cfb.FullPaths[j] = new_name;
				return true;
			}
			return false;
		}

		function cfb_gc(cfb) { rebuild_cfb(cfb, true); }

		exports.find = find;
		exports.read = read;
		exports.parse = parse;
		exports.write = write;
		exports.writeFile = write_file;
		exports.utils = {
			cfb_new: cfb_new,
			cfb_add: cfb_add,
			cfb_del: cfb_del,
			cfb_mov: cfb_mov,
			cfb_gc: cfb_gc,
			ReadShift: ReadShift,
			CheckField: CheckField,
			prep_blob: prep_blob,
			bconcat: bconcat,
			use_zlib: use_zlib,
			_deflateRaw: _deflate,
			_inflateRaw: _inflate,
			consts: consts
		};

		return exports;
		})();

		if(typeof DO_NOT_EXPORT_CFB === 'undefined') { module.exports = CFB; } 
	} (cfb));
	return cfb.exports;
}

var hasRequiredCfbInterop;

function requireCfbInterop () {
	if (hasRequiredCfbInterop) return cfbInterop;
	hasRequiredCfbInterop = 1;
	Object.defineProperty(cfbInterop, "__esModule", { value: true });
	cfbInterop.CFB = void 0;
	const CFBModule = requireCfb();
	function resolveCfb() {
	    if (typeof CFBModule.read === 'function') {
	        return CFBModule;
	    }
	    const wrapped = CFBModule.default;
	    if (wrapped !== undefined && typeof wrapped.read === 'function') {
	        return wrapped;
	    }
	    const shape = Object.keys(CFBModule)
	        .map((key) => `${key}:${typeof CFBModule[key]}`)
	        .join(',');
	    throw new Error(`cfb library surface not found on module namespace or default export (namespace: [${shape}])`);
	}
	cfbInterop.CFB = resolveCfb();
	
	return cfbInterop;
}

var BinaryReader = {};

var hasRequiredBinaryReader;

function requireBinaryReader () {
	if (hasRequiredBinaryReader) return BinaryReader;
	hasRequiredBinaryReader = 1;
	Object.defineProperty(BinaryReader, "__esModule", { value: true });
	BinaryReader.BinaryReader = void 0;
	const Util_1 = requireUtil();
	class BinaryReader$1 {
	    static async fromBlob(blob) {
	        const arrayBuffer = await blob.arrayBuffer();
	        return new BinaryReader$1(arrayBuffer);
	    }
	    constructor(buffer) {
	        this.offset = 0;
	        if (ArrayBuffer.isView(buffer)) {
	            this.view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
	        }
	        else {
	            this.view = new DataView(buffer);
	        }
	    }
	    readUint8() {
	        const value = this.view.getUint8(this.offset);
	        this.offset += 1;
	        return value;
	    }
	    readUint16LE() {
	        const value = this.view.getUint16(this.offset, true);
	        this.offset += 2;
	        return value;
	    }
	    readUint32LE() {
	        const value = this.view.getUint32(this.offset, true);
	        this.offset += 4;
	        return value;
	    }
	    readInt32LE() {
	        const value = this.view.getInt32(this.offset, true);
	        this.offset += 4;
	        return value;
	    }
	    readFloat64LE() {
	        const value = this.view.getFloat64(this.offset, true);
	        this.offset += 8;
	        return value;
	    }
	    readBytes(length) {
	        if (length < 0) {
	            throw new Error(`[ReadBytes_InvalidLength] Cannot read negative length: ${length}`);
	        }
	        if (this.offset + length > this.view.byteLength) {
	            throw new Error(`[ReadBytes_OutOfBounds] Cannot read ${length} bytes at offset ${this.offset}, buffer size is ${this.view.byteLength}`);
	        }
	        const bytes = new Uint8Array(this.view.buffer, this.view.byteOffset + this.offset, length);
	        this.offset += length;
	        return bytes;
	    }
	    readPascalString() {
	        const length = this.readUint32LE();
	        if (length === 0) {
	            return '';
	        }
	        const bytes = this.readBytes(length);
	        return (0, Util_1.decodeWindows1252)(bytes);
	    }
	    readPascalStringShort() {
	        const length = this.readUint8();
	        if (length === 0) {
	            return '';
	        }
	        const bytes = this.readBytes(length);
	        return (0, Util_1.decodeWindows1252)(bytes);
	    }
	    readUTF16String(length) {
	        const bytes = this.readBytes(length);
	        return new TextDecoder('utf-16le').decode(bytes);
	    }
	    seek(offset) {
	        if (offset < 0 || offset > this.view.byteLength) {
	            throw new Error(`[Seek_OutOfBounds] Cannot seek to offset ${offset}, buffer size is ${this.view.byteLength}`);
	        }
	        this.offset = offset;
	    }
	    skip(bytes) {
	        const newOffset = this.offset + bytes;
	        if (newOffset < 0 || newOffset > this.view.byteLength) {
	            throw new Error(`[Skip_OutOfBounds] Cannot skip ${bytes} bytes from offset ${this.offset}, buffer size is ${this.view.byteLength}`);
	        }
	        this.offset = newOffset;
	    }
	    remaining() {
	        return this.view.byteLength - this.offset;
	    }
	    getPosition() {
	        return this.offset;
	    }
	    hasData() {
	        return this.offset < this.view.byteLength;
	    }
	    getByteLength() {
	        return this.view.byteLength;
	    }
	}
	BinaryReader.BinaryReader = BinaryReader$1;
	
	return BinaryReader;
}

var PropertyParser = {};

var ModelText = {};

var AltiumTextCodec = {};

var hasRequiredAltiumTextCodec;

function requireAltiumTextCodec () {
	if (hasRequiredAltiumTextCodec) return AltiumTextCodec;
	hasRequiredAltiumTextCodec = 1;
	Object.defineProperty(AltiumTextCodec, "__esModule", { value: true });
	AltiumTextCodec.AltiumTextCodec = void 0;
	class AltiumTextCodec$1 {
	    decode(raw) {
	        let text = '';
	        const transforms = [];
	        let index = 0;
	        while (index < raw.length) {
	            const characters = [];
	            let end = index;
	            let hasTrailingTerminator = false;
	            if (raw[index] === '\\') {
	                while (end + 1 < raw.length && raw[end] === '\\' && raw[end + 1] !== '\\') {
	                    characters.push(raw[end + 1]);
	                    end += 2;
	                }
	                if (end < raw.length && raw[end] === '\\' && end + 1 === raw.length) {
	                    end++;
	                    hasTrailingTerminator = true;
	                }
	            }
	            else if (raw[index] !== ':' && index + 1 < raw.length && raw[index + 1] === '\\') {
	                characters.push(raw[index]);
	                end++;
	                while (end + 1 < raw.length && raw[end] === '\\' && raw[end + 1] !== '\\') {
	                    characters.push(raw[end + 1]);
	                    end += 2;
	                }
	                if (end < raw.length && raw[end] === '\\' && end + 1 === raw.length) {
	                    end++;
	                    hasTrailingTerminator = true;
	                }
	            }
	            if (characters.length > 0 && (characters.length > 1 || hasTrailingTerminator)) {
	                const from = text.length;
	                text += characters.join('');
	                transforms.push({ type: 'bar', from, to: text.length });
	                index = end;
	                continue;
	            }
	            text += raw[index];
	            index++;
	        }
	        return { text, transforms };
	    }
	}
	AltiumTextCodec.AltiumTextCodec = AltiumTextCodec$1;
	
	return AltiumTextCodec;
}

var CadstromTextCodec = {};

var hasRequiredCadstromTextCodec;

function requireCadstromTextCodec () {
	if (hasRequiredCadstromTextCodec) return CadstromTextCodec;
	hasRequiredCadstromTextCodec = 1;
	Object.defineProperty(CadstromTextCodec, "__esModule", { value: true });
	CadstromTextCodec.CadstromTextCodec = void 0;
	class CadstromTextCodec$1 {
	    decode(raw) {
	        let text = '';
	        const transforms = [];
	        let index = 0;
	        const prefix = '\\overline{';
	        while (index < raw.length) {
	            if (raw.substring(index, index + prefix.length) === prefix) {
	                const close = raw.indexOf('}', index + prefix.length);
	                if (close > index + prefix.length) {
	                    const from = text.length;
	                    text += raw.substring(index + prefix.length, close);
	                    transforms.push({ type: 'bar', from, to: text.length });
	                    index = close + 1;
	                    continue;
	                }
	            }
	            text += raw[index];
	            index++;
	        }
	        return { text, transforms };
	    }
	}
	CadstromTextCodec.CadstromTextCodec = CadstromTextCodec$1;
	
	return CadstromTextCodec;
}

var KicadTextCodec = {};

var hasRequiredKicadTextCodec;

function requireKicadTextCodec () {
	if (hasRequiredKicadTextCodec) return KicadTextCodec;
	hasRequiredKicadTextCodec = 1;
	Object.defineProperty(KicadTextCodec, "__esModule", { value: true });
	KicadTextCodec.KicadTextCodec = void 0;
	class KicadTextCodec$1 {
	    decode(raw) {
	        let text = '';
	        const transforms = [];
	        let index = 0;
	        while (index < raw.length) {
	            if (raw.substring(index, index + 2) === '~{') {
	                const close = raw.indexOf('}', index + 2);
	                if (close > index + 2) {
	                    const from = text.length;
	                    text += raw.substring(index + 2, close);
	                    transforms.push({ type: 'bar', from, to: text.length });
	                    index = close + 1;
	                    continue;
	                }
	            }
	            text += raw[index];
	            index++;
	        }
	        return { text, transforms };
	    }
	}
	KicadTextCodec.KicadTextCodec = KicadTextCodec$1;
	
	return KicadTextCodec;
}

var OrcadTextCodec = {};

var hasRequiredOrcadTextCodec;

function requireOrcadTextCodec () {
	if (hasRequiredOrcadTextCodec) return OrcadTextCodec;
	hasRequiredOrcadTextCodec = 1;
	Object.defineProperty(OrcadTextCodec, "__esModule", { value: true });
	OrcadTextCodec.OrcadTextCodec = void 0;
	class OrcadTextCodec$1 {
	    decode(raw) {
	        let text = '';
	        const transforms = [];
	        let inputIndex = 0;
	        while (inputIndex < raw.length) {
	            const hasOpeningMarker = raw.substring(inputIndex, inputIndex + 2) === '\\\\';
	            const runStart = inputIndex + (hasOpeningMarker ? 2 : 0);
	            let runEnd = runStart;
	            let runLength = 0;
	            while (runEnd + 1 < raw.length && raw[runEnd] !== '\\' && raw[runEnd + 1] === '\\') {
	                runLength++;
	                runEnd += 2;
	            }
	            if (runLength > 0 && (hasOpeningMarker || runLength > 1)) {
	                const from = text.length;
	                for (let index = runStart; index < runEnd; index += 2) {
	                    text += raw[index];
	                }
	                transforms.push({ type: 'bar', from, to: text.length });
	                inputIndex = runEnd;
	                continue;
	            }
	            text += raw[inputIndex];
	            inputIndex++;
	        }
	        return { text, transforms };
	    }
	}
	OrcadTextCodec.OrcadTextCodec = OrcadTextCodec$1;
	
	return OrcadTextCodec;
}

var hasRequiredModelText;

function requireModelText () {
	if (hasRequiredModelText) return ModelText;
	hasRequiredModelText = 1;
	Object.defineProperty(ModelText, "__esModule", { value: true });
	ModelText.MODEL_TEXT_MIN_VERSION = ModelText.TextDialect = void 0;
	ModelText.getTextDialectForCadTool = getTextDialectForCadTool;
	ModelText.getModelTextCodec = getModelTextCodec;
	ModelText.resolveDisplayText = resolveDisplayText;
	ModelText.encodeStrippedText = encodeStrippedText;
	ModelText.encodeRawText = encodeRawText;
	ModelText.displayLength = displayLength;
	ModelText.toPlainText = toPlainText;
	ModelText.toUnicodeOverlineText = toUnicodeOverlineText;
	ModelText.searchHaystack = searchHaystack;
	const AltiumTextCodec_1 = requireAltiumTextCodec();
	const CadstromTextCodec_1 = requireCadstromTextCodec();
	const KicadTextCodec_1 = requireKicadTextCodec();
	const OrcadTextCodec_1 = requireOrcadTextCodec();
	var TextDialect;
	(function (TextDialect) {
	    TextDialect["None"] = "none";
	    TextDialect["Orcad"] = "orcad";
	    TextDialect["Altium"] = "altium";
	    TextDialect["Kicad"] = "kicad";
	    TextDialect["Cadstrom"] = "cadstrom";
	})(TextDialect || (ModelText.TextDialect = TextDialect = {}));
	ModelText.MODEL_TEXT_MIN_VERSION = '1.0.0';
	function getTextDialectForCadTool(tool) {
	    switch (tool) {
	        case 'orcad':
	            return TextDialect.Orcad;
	        case 'altium':
	            return TextDialect.Altium;
	        case 'kicad':
	            return TextDialect.Kicad;
	        case 'cadstrom':
	            return TextDialect.Cadstrom;
	        case 'eagle':
	        case 'allegro':
	        case 'xpedition':
	            return TextDialect.None;
	    }
	}
	function getModelTextCodec(dialect) {
	    switch (dialect) {
	        case TextDialect.Orcad:
	            return new OrcadTextCodec_1.OrcadTextCodec();
	        case TextDialect.Altium:
	            return new AltiumTextCodec_1.AltiumTextCodec();
	        case TextDialect.Kicad:
	            return new KicadTextCodec_1.KicadTextCodec();
	        case TextDialect.Cadstrom:
	            return new CadstromTextCodec_1.CadstromTextCodec();
	        case TextDialect.None:
	            return new NoneTextCodec();
	    }
	}
	class NoneTextCodec {
	    decode(raw) {
	        return { text: raw, transforms: [] };
	    }
	}
	function resolveDisplayText(source) {
	    if (source.declarations?.overbar) {
	        return { text: source.raw, transforms: [{ type: 'bar', from: 0, to: source.raw.length }] };
	    }
	    let text = source.raw;
	    let transforms = [];
	    for (const dialect of source.dialects) {
	        const decoded = getModelTextCodec(dialect).decode(text);
	        const offsets = offsetsAfterRemovals(text, decoded.text);
	        transforms = remapBars(transforms, offsets);
	        transforms.push(...decoded.transforms);
	        text = decoded.text;
	    }
	    return { text, transforms: normalizeBarTransforms(transforms) };
	}
	function encodeStrippedText(source) {
	    const display = resolveDisplayText(source);
	    const textTransforms = display.transforms.filter(isBarTransform);
	    return textTransforms.length > 0 ? { value: display.text, textTransforms } : { value: display.text };
	}
	function encodeRawText(source) {
	    const display = resolveDisplayText(source);
	    return {
	        value: source.raw,
	        textTransforms: [...replacementTransforms(source.raw, display.text), ...display.transforms.filter(isBarTransform)],
	    };
	}
	function displayLength(source) {
	    return resolveDisplayText(source).text.length;
	}
	function toPlainText(display) {
	    return display.text;
	}
	function toUnicodeOverlineText(display) {
	    const bars = display.transforms.filter(isBarTransform);
	    let text = '';
	    for (let index = 0; index < display.text.length; index++) {
	        text += display.text[index];
	        if (bars.some((bar) => index >= bar.from && index < bar.to)) {
	            text += '\u0305';
	        }
	    }
	    return text;
	}
	function searchHaystack(source) {
	    return [...new Set([source.raw, toPlainText(resolveDisplayText(source))])];
	}
	function isBarTransform(transform) {
	    return transform.type === 'bar';
	}
	function offsetsAfterRemovals(source, target) {
	    const offsets = [];
	    let targetIndex = 0;
	    for (let index = 0; index < source.length; index++) {
	        offsets.push(targetIndex);
	        if (source[index] === target[targetIndex]) {
	            targetIndex++;
	        }
	    }
	    offsets.push(targetIndex);
	    return offsets;
	}
	function remapBars(transforms, offsets) {
	    return transforms.map((transform) => transform.type === 'bar' ? { ...transform, from: offsets[transform.from], to: offsets[transform.to] } : transform);
	}
	function normalizeBarTransforms(transforms) {
	    const bars = transforms.filter(isBarTransform).sort((left, right) => left.from - right.from || left.to - right.to);
	    const normalizedBars = [];
	    for (const bar of bars) {
	        const previous = normalizedBars.at(-1);
	        if (previous && bar.from <= previous.to) {
	            previous.to = Math.max(previous.to, bar.to);
	        }
	        else {
	            normalizedBars.push({ ...bar });
	        }
	    }
	    return [...transforms.filter((transform) => !isBarTransform(transform)), ...normalizedBars];
	}
	function replacementTransforms(source, display) {
	    const transforms = [];
	    let sourceIndex = 0;
	    let displayIndex = 0;
	    let outputIndex = 0;
	    while (sourceIndex < source.length) {
	        if (source[sourceIndex] === display[displayIndex]) {
	            sourceIndex++;
	            displayIndex++;
	            outputIndex++;
	            continue;
	        }
	        const start = outputIndex;
	        let length = 0;
	        while (sourceIndex < source.length && source[sourceIndex] !== display[displayIndex]) {
	            sourceIndex++;
	            length++;
	        }
	        transforms.push({ type: 'replace', start, length, value: '' });
	    }
	    return transforms;
	}
	
	return ModelText;
}

var hasRequiredPropertyParser;

function requirePropertyParser () {
	if (hasRequiredPropertyParser) return PropertyParser;
	hasRequiredPropertyParser = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PropertyParser = exports.AltiumPropertiesSchema = exports.AltiumPropertySchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const Util_1 = requireUtil();
		const ModelText_1 = requireModelText();
		exports.AltiumPropertySchema = zod_1.default.object({
		    key: zod_1.default.string(),
		    value: zod_1.default.string(),
		});
		exports.AltiumPropertiesSchema = zod_1.default.record(zod_1.default.string(), exports.AltiumPropertySchema.optional());
		class PropertyParser {
		    static parseProperties(data) {
		        if (data.length === 0) {
		            return {};
		        }
		        if (data.length >= 2 && data[0] === 0xff && data[1] === 0xfe) {
		            let cleanData = data.subarray(2);
		            if (cleanData.length >= 2 && cleanData[cleanData.length - 2] === 0 && cleanData[cleanData.length - 1] === 0) {
		                cleanData = cleanData.subarray(0, -2);
		            }
		            return this.parsePropertyString(new TextDecoder('utf-16le').decode(cleanData));
		        }
		        let cleanData = data;
		        if (data[data.length - 1] === 0) {
		            cleanData = data.subarray(0, -1);
		        }
		        const utf8Prefix = '%UTF8%';
		        if (cleanData.length >= utf8Prefix.length &&
		            cleanData[0] === 0x25 &&
		            (0, Util_1.bytesToLatin1)(cleanData.subarray(0, utf8Prefix.length)) === utf8Prefix) {
		            return this.parsePropertyString(new TextDecoder('utf-8').decode(cleanData.subarray(utf8Prefix.length)));
		        }
		        return this.parsePropertyPairs((0, Util_1.bytesToLatin1)(cleanData));
		    }
		    static parsePropertyPairs(byteLine) {
		        const properties = {};
		        const lockedByUtf8 = new Set();
		        const utf8Prefix = '%UTF8%';
		        for (const pair of this.splitPropertyPairs(byteLine)) {
		            const equalIndex = pair.indexOf('=');
		            let key = equalIndex === -1 ? pair : pair.substring(0, equalIndex);
		            const rawValue = equalIndex === -1 ? '' : pair.substring(equalIndex + 1);
		            const isUtf8 = key.startsWith(utf8Prefix);
		            if (isUtf8) {
		                key = key.substring(utf8Prefix.length);
		            }
		            const normalizedKey = key.trim().toLowerCase();
		            if (normalizedKey.length === 0) {
		                continue;
		            }
		            const value = isUtf8 ? new TextDecoder('utf-8').decode(this.byteStringToBytes(rawValue)) : (0, Util_1.latin1ToWindows1252)(rawValue);
		            if (isUtf8) {
		                const representable = (0, Util_1.isWindows1252Representable)(value);
		                if (representable || properties[normalizedKey] === undefined) {
		                    properties[normalizedKey] = { key, value };
		                }
		                if (representable) {
		                    lockedByUtf8.add(normalizedKey);
		                }
		            }
		            else if (!lockedByUtf8.has(normalizedKey)) {
		                properties[normalizedKey] = { key, value };
		            }
		        }
		        return properties;
		    }
		    static byteStringToBytes(byteString) {
		        const bytes = new Uint8Array(byteString.length);
		        for (let i = 0; i < byteString.length; i++) {
		            bytes[i] = byteString.charCodeAt(i) & 0xff;
		        }
		        return bytes;
		    }
		    static parsePropertyString(text) {
		        const properties = {};
		        if (text.length === 0) {
		            return properties;
		        }
		        const pairs = this.splitPropertyPairs(text);
		        for (const pair of pairs) {
		            const equalIndex = pair.indexOf('=');
		            if (equalIndex === -1) {
		                const key = pair;
		                if (key.length > 0) {
		                    properties[key.trim().toLowerCase()] = { key, value: '' };
		                }
		                continue;
		            }
		            const key = pair.substring(0, equalIndex);
		            const value = pair.substring(equalIndex + 1);
		            if (key.length > 0) {
		                properties[key.trim().toLowerCase()] = { key, value };
		            }
		        }
		        return properties;
		    }
		    static convertTildeOneToNewline(text) {
		        return text.replace(/~1/g, '\n');
		    }
		    static splitPropertyPairs(text) {
		        const pairs = [];
		        let start = 0;
		        let continuedPair = '';
		        let pipe = text.indexOf('|');
		        while (pipe !== -1) {
		            if (text[pipe + 1] === '>') {
		                continuedPair += text.substring(start, pipe) + '\n';
		                start = pipe + 2;
		            }
		            else {
		                const pair = continuedPair + text.substring(start, pipe);
		                if (pair.length > 0) {
		                    pairs.push(pair);
		                }
		                continuedPair = '';
		                start = pipe + 1;
		            }
		            pipe = text.indexOf('|', start);
		        }
		        const finalPair = continuedPair + text.substring(start);
		        if (finalPair.length > 0) {
		            pairs.push(finalPair);
		        }
		        return pairs;
		    }
		    static parsePropertyLength(lengthRaw) {
		        const isBinary = (lengthRaw & 0xff000000) !== 0;
		        const length = lengthRaw & 0x00ffffff;
		        return { isBinary, length };
		    }
		    static parseAltiumTextTransforms(text) {
		        const isNetRef = text.value instanceof sigma_engine_shared_1.NetRef;
		        const encoded = this.encodeAltiumDisplayText(isNetRef ? String(text.value) : text.value);
		        if (isNetRef) {
		            text.value.setName(encoded.value);
		        }
		        else {
		            text.value = encoded.value;
		        }
		        if (encoded.textTransforms) {
		            text.textTransforms = [...(text.textTransforms ?? []), ...encoded.textTransforms];
		        }
		        return text;
		    }
		    static encodeAltiumDisplayText(value) {
		        const encoded = (0, ModelText_1.encodeStrippedText)({
		            raw: this.convertTildeOneToNewline(value),
		            dialects: [ModelText_1.TextDialect.Altium],
		        });
		        return encoded.textTransforms ? encoded : { value: encoded.value };
		    }
		}
		exports.PropertyParser = PropertyParser;
		
	} (PropertyParser));
	return PropertyParser;
}

var cfbUtil = {};

var hasRequiredCfbUtil;

function requireCfbUtil () {
	if (hasRequiredCfbUtil) return cfbUtil;
	hasRequiredCfbUtil = 1;
	Object.defineProperty(cfbUtil, "__esModule", { value: true });
	cfbUtil.loadCfb = loadCfb;
	const cfb_interop_1 = requireCfbInterop();
	async function loadCfb(fileContents) {
	    const arrayBuffer = await fileContents.arrayBuffer();
	    const u8 = new Uint8Array(arrayBuffer);
	    return cfb_interop_1.CFB.read(u8, { type: 'array' });
	}
	
	return cfbUtil;
}

var hasRequiredStreamParser;

function requireStreamParser () {
	if (hasRequiredStreamParser) return StreamParser;
	hasRequiredStreamParser = 1;
	Object.defineProperty(StreamParser, "__esModule", { value: true });
	StreamParser.StreamParser = void 0;
	const cfb_interop_1 = requireCfbInterop();
	const sigma_engine_shared_1 = requireDist$1();
	const BinaryReader_1 = requireBinaryReader();
	const PropertyParser_1 = requirePropertyParser();
	const cfbUtil_1 = requireCfbUtil();
	const STORAGE_ENTRY_MAGIC = 0xd0;
	class StreamParser$1 {
	    constructor(cfb) {
	        this.cfb = cfb;
	    }
	    static async fromBlob(blob) {
	        const cfb = await (0, cfbUtil_1.loadCfb)(blob);
	        return new StreamParser$1(cfb);
	    }
	    async parseStorage() {
	        const stream = cfb_interop_1.CFB.find(this.cfb, 'Storage');
	        if (stream === null) {
	            return undefined;
	        }
	        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
	        const blob = new Blob([content]);
	        const reader = await BinaryReader_1.BinaryReader.fromBlob(blob);
	        if (reader.getByteLength() === 0) {
	            return undefined;
	        }
	        const propertyLength = reader.readUint32LE();
	        const propertyData = reader.readBytes(propertyLength);
	        const properties = PropertyParser_1.PropertyParser.parseProperties(propertyData);
	        const weight = parseInt(properties.weight?.value ?? '0', 10);
	        if (!reader.hasData()) {
	            return { properties, numFiles: 0, files: [], processingResults: [] };
	        }
	        const files = [];
	        const processingResults = [];
	        for (let i = 0; i < weight; i++) {
	            const { length: declaredLength } = PropertyParser_1.PropertyParser.parsePropertyLength(reader.readUint32LE());
	            const bodyStart = reader.getPosition();
	            const body = new BinaryReader_1.BinaryReader(reader.readBytes(declaredLength));
	            let entry;
	            try {
	                entry = StreamParser$1.parseStorageEntry(body);
	            }
	            catch (error) {
	                const reason = error instanceof Error ? error.message : String(error);
	                processingResults.push({
	                    type: 'ParseStorage_EntrySkipped',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    message: `[ParseStorage_EntrySkipped] Entry ${i} at offset ${bodyStart} (${declaredLength} bytes) could not be read: ${reason}`,
	                    entryIndex: i,
	                    declaredLength,
	                    reason,
	                });
	                continue;
	            }
	            files.push(entry.file);
	            if (entry.consumedLength !== declaredLength) {
	                processingResults.push({
	                    type: 'ParseStorage_EntryLengthMismatch',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    message: `[ParseStorage_EntryLengthMismatch] Entry '${entry.file.fileName}' declares ${declaredLength} bytes but its fields span ${entry.consumedLength}`,
	                    fileName: entry.file.fileName,
	                    declaredLength,
	                    consumedLength: entry.consumedLength,
	                    dataLength: entry.dataLength,
	                });
	            }
	        }
	        return { properties, numFiles: weight, files, processingResults };
	    }
	    static parseStorageEntry(body) {
	        const magic = body.readUint8();
	        if (magic !== STORAGE_ENTRY_MAGIC) {
	            throw new Error(`[ParseStorage_InvalidMagic] Expected 0xD0, got 0x${magic.toString(16)}`);
	        }
	        const fileName = body.readPascalStringShort();
	        const dataLength = body.readUint32LE();
	        const data = body.readBytes(Math.min(dataLength, body.remaining()));
	        const consumedLength = body.getPosition() + Math.max(0, dataLength - data.length);
	        return { file: { fileName, data }, consumedLength, dataLength };
	    }
	    async parseFileHeader() {
	        const stream = cfb_interop_1.CFB.find(this.cfb, 'FileHeader');
	        if (stream === null) {
	            throw new Error('[ParseFileHeader_StreamNotFound] FileHeader stream is required but not found');
	        }
	        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
	        const reader = new BinaryReader_1.BinaryReader(content);
	        const records = [];
	        while (reader.hasData()) {
	            const lengthRaw = reader.readUint32LE();
	            const { length } = PropertyParser_1.PropertyParser.parsePropertyLength(lengthRaw);
	            const propertyData = reader.readBytes(length);
	            const properties = PropertyParser_1.PropertyParser.parseProperties(propertyData);
	            records.push(properties);
	        }
	        return records;
	    }
	    async parseAdditional() {
	        const stream = cfb_interop_1.CFB.find(this.cfb, 'Additional');
	        if (stream === null) {
	            return undefined;
	        }
	        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
	        const reader = new BinaryReader_1.BinaryReader(content);
	        const records = [];
	        while (reader.hasData()) {
	            const lengthRaw = reader.readUint32LE();
	            const { length } = PropertyParser_1.PropertyParser.parsePropertyLength(lengthRaw);
	            const propertyData = reader.readBytes(length);
	            const properties = PropertyParser_1.PropertyParser.parseProperties(propertyData);
	            records.push(properties);
	        }
	        return records;
	    }
	    findStream(path) {
	        const stream = cfb_interop_1.CFB.find(this.cfb, path);
	        if (stream === null) {
	            return undefined;
	        }
	        const content = Array.isArray(stream.content) ? new Uint8Array(stream.content) : stream.content;
	        return content;
	    }
	}
	StreamParser.StreamParser = StreamParser$1;
	
	return StreamParser;
}

var AltiumSchematic = {};

var AltiumComponent = {};

var SchDocTypes = {};

var hasRequiredSchDocTypes;

function requireSchDocTypes () {
	if (hasRequiredSchDocTypes) return SchDocTypes;
	hasRequiredSchDocTypes = 1;
	Object.defineProperty(SchDocTypes, "__esModule", { value: true });
	SchDocTypes.RecordType = void 0;
	SchDocTypes.isGraphicRecord = isGraphicRecord;
	var RecordType;
	(function (RecordType) {
	    RecordType[RecordType["HEADER"] = 0] = "HEADER";
	    RecordType[RecordType["COMPONENT"] = 1] = "COMPONENT";
	    RecordType[RecordType["PIN"] = 2] = "PIN";
	    RecordType[RecordType["IEEE_SYMBOL"] = 3] = "IEEE_SYMBOL";
	    RecordType[RecordType["LABEL"] = 4] = "LABEL";
	    RecordType[RecordType["BEZIER"] = 5] = "BEZIER";
	    RecordType[RecordType["POLYLINE"] = 6] = "POLYLINE";
	    RecordType[RecordType["POLYGON"] = 7] = "POLYGON";
	    RecordType[RecordType["ELLIPSE"] = 8] = "ELLIPSE";
	    RecordType[RecordType["PIECHART"] = 9] = "PIECHART";
	    RecordType[RecordType["ROUND_RECTANGLE"] = 10] = "ROUND_RECTANGLE";
	    RecordType[RecordType["ELLIPTICAL_ARC"] = 11] = "ELLIPTICAL_ARC";
	    RecordType[RecordType["ARC"] = 12] = "ARC";
	    RecordType[RecordType["LINE"] = 13] = "LINE";
	    RecordType[RecordType["RECTANGLE"] = 14] = "RECTANGLE";
	    RecordType[RecordType["SHEET_SYMBOL"] = 15] = "SHEET_SYMBOL";
	    RecordType[RecordType["SHEET_ENTRY"] = 16] = "SHEET_ENTRY";
	    RecordType[RecordType["POWER_PORT"] = 17] = "POWER_PORT";
	    RecordType[RecordType["PORT"] = 18] = "PORT";
	    RecordType[RecordType["UNKNOWN_LEGACY"] = 21] = "UNKNOWN_LEGACY";
	    RecordType[RecordType["NO_ERC"] = 22] = "NO_ERC";
	    RecordType[RecordType["NET_LABEL"] = 25] = "NET_LABEL";
	    RecordType[RecordType["BUS"] = 26] = "BUS";
	    RecordType[RecordType["WIRE"] = 27] = "WIRE";
	    RecordType[RecordType["TEXT_FRAME"] = 28] = "TEXT_FRAME";
	    RecordType[RecordType["JUNCTION"] = 29] = "JUNCTION";
	    RecordType[RecordType["IMAGE"] = 30] = "IMAGE";
	    RecordType[RecordType["SHEET"] = 31] = "SHEET";
	    RecordType[RecordType["SHEET_NAME"] = 32] = "SHEET_NAME";
	    RecordType[RecordType["FILE_NAME"] = 33] = "FILE_NAME";
	    RecordType[RecordType["DESIGNATOR"] = 34] = "DESIGNATOR";
	    RecordType[RecordType["BUS_ENTRY"] = 37] = "BUS_ENTRY";
	    RecordType[RecordType["TEMPLATE"] = 39] = "TEMPLATE";
	    RecordType[RecordType["PARAMETER"] = 41] = "PARAMETER";
	    RecordType[RecordType["PARAMETER_SET"] = 43] = "PARAMETER_SET";
	    RecordType[RecordType["IMPLEMENTATION_LIST"] = 44] = "IMPLEMENTATION_LIST";
	    RecordType[RecordType["IMPLEMENTATION"] = 45] = "IMPLEMENTATION";
	    RecordType[RecordType["MAP_DEFINER_LIST"] = 46] = "MAP_DEFINER_LIST";
	    RecordType[RecordType["MAP_DEFINER"] = 47] = "MAP_DEFINER";
	    RecordType[RecordType["IMPL_PARAMS"] = 48] = "IMPL_PARAMS";
	    RecordType[RecordType["NOTE"] = 209] = "NOTE";
	    RecordType[RecordType["SIGNAL_PROBE"] = 210] = "SIGNAL_PROBE";
	    RecordType[RecordType["COMPILE_MASK"] = 211] = "COMPILE_MASK";
	    RecordType[RecordType["HARNESS_CONNECTOR"] = 215] = "HARNESS_CONNECTOR";
	    RecordType[RecordType["HARNESS_ENTRY"] = 216] = "HARNESS_ENTRY";
	    RecordType[RecordType["HARNESS_TYPE"] = 217] = "HARNESS_TYPE";
	    RecordType[RecordType["SIGNAL_HARNESS"] = 218] = "SIGNAL_HARNESS";
	    RecordType[RecordType["CODE_SYMBOL"] = 220] = "CODE_SYMBOL";
	    RecordType[RecordType["CODE_SYMBOL_ENTRY"] = 221] = "CODE_SYMBOL_ENTRY";
	    RecordType[RecordType["CODE_SYMBOL_NAME"] = 222] = "CODE_SYMBOL_NAME";
	})(RecordType || (SchDocTypes.RecordType = RecordType = {}));
	function isGraphicRecord(record) {
	    return (record.recordType === RecordType.LINE ||
	        record.recordType === RecordType.RECTANGLE ||
	        record.recordType === RecordType.POLYLINE ||
	        record.recordType === RecordType.POLYGON ||
	        record.recordType === RecordType.ARC ||
	        record.recordType === RecordType.BEZIER ||
	        record.recordType === RecordType.ELLIPSE ||
	        record.recordType === RecordType.ELLIPTICAL_ARC ||
	        record.recordType === RecordType.ROUND_RECTANGLE);
	}
	
	return SchDocTypes;
}

var hasRequiredAltiumComponent;

function requireAltiumComponent () {
	if (hasRequiredAltiumComponent) return AltiumComponent;
	hasRequiredAltiumComponent = 1;
	Object.defineProperty(AltiumComponent, "__esModule", { value: true });
	AltiumComponent.AltiumComponent = void 0;
	const SchDocTypes_1 = requireSchDocTypes();
	class AltiumComponent$1 {
	    constructor(schematic, record) {
	        this.schematic = schematic;
	        this.record = record;
	        this.childRecords = this.schematic.getChildRecords(this.record.index);
	    }
	    getPins() {
	        return this.childRecords.filter((r) => r.recordType === SchDocTypes_1.RecordType.PIN);
	    }
	    getHiddenNetName(pin) {
	        const parameters = this.schematic
	            .getChildRecords(pin.index)
	            .filter((record) => record.recordType === SchDocTypes_1.RecordType.PARAMETER);
	        const parameter = parameters.find((record) => record.name.toLowerCase() === 'hiddennetname');
	        const name = parameter?.text.trim();
	        return name ? name : undefined;
	    }
	    getDesignator() {
	        return this.childRecords.find((r) => r.recordType === SchDocTypes_1.RecordType.DESIGNATOR);
	    }
	    getParameters() {
	        return this.childRecords.filter((r) => r.recordType === SchDocTypes_1.RecordType.PARAMETER);
	    }
	    getImplementationLists() {
	        return this.childRecords
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.IMPLEMENTATION_LIST)
	            .map((r) => this.schematic.getImplementationListByIndex(r.index));
	    }
	    getDesignatorText() {
	        return this.getDesignator()?.text;
	    }
	    getParameterValue(name) {
	        const param = this.getParameters().find((p) => p.name === name);
	        return param?.text;
	    }
	    getGraphics() {
	        return this.childRecords.filter(SchDocTypes_1.isGraphicRecord);
	    }
	}
	AltiumComponent.AltiumComponent = AltiumComponent$1;
	
	return AltiumComponent;
}

var AltiumHarnessConnector = {};

var hasRequiredAltiumHarnessConnector;

function requireAltiumHarnessConnector () {
	if (hasRequiredAltiumHarnessConnector) return AltiumHarnessConnector;
	hasRequiredAltiumHarnessConnector = 1;
	Object.defineProperty(AltiumHarnessConnector, "__esModule", { value: true });
	AltiumHarnessConnector.AltiumHarnessConnector = void 0;
	const SchDocTypes_1 = requireSchDocTypes();
	class AltiumHarnessConnector$1 {
	    constructor(schematic, record) {
	        this.schematic = schematic;
	        this.record = record;
	    }
	    getEntries() {
	        const children = this.schematic.getHarnessConnectorChildren(this.record.index);
	        return children.filter((r) => r.recordType === SchDocTypes_1.RecordType.HARNESS_ENTRY);
	    }
	    getHarnessType() {
	        const children = this.schematic.getHarnessConnectorChildren(this.record.index);
	        return children.find((r) => r.recordType === SchDocTypes_1.RecordType.HARNESS_TYPE);
	    }
	    getHarnessTypeText() {
	        return this.getHarnessType()?.text;
	    }
	    getEntryByName(name) {
	        return this.getEntries().find((e) => e.name === name);
	    }
	}
	AltiumHarnessConnector.AltiumHarnessConnector = AltiumHarnessConnector$1;
	
	return AltiumHarnessConnector;
}

var AltiumImplementationList = {};

var hasRequiredAltiumImplementationList;

function requireAltiumImplementationList () {
	if (hasRequiredAltiumImplementationList) return AltiumImplementationList;
	hasRequiredAltiumImplementationList = 1;
	Object.defineProperty(AltiumImplementationList, "__esModule", { value: true });
	AltiumImplementationList.AltiumImplementationList = void 0;
	const SchDocTypes_1 = requireSchDocTypes();
	class AltiumImplementationList$1 {
	    constructor(schematic, record) {
	        this.schematic = schematic;
	        this.record = record;
	    }
	    getImplementations() {
	        const children = this.schematic.getChildRecords(this.record.index);
	        return children.filter((r) => r.recordType === SchDocTypes_1.RecordType.IMPLEMENTATION);
	    }
	    hasImplementations() {
	        return this.getImplementations().length > 0;
	    }
	    getCurrentImplementation() {
	        return this.getImplementations().find((impl) => impl.isCurrent);
	    }
	    hasCurrentImplementation() {
	        return this.getCurrentImplementation() !== undefined;
	    }
	    getImplementationsByType(modelType) {
	        return this.getImplementations().filter((impl) => impl.modelType === modelType);
	    }
	    getPcbLibImplementation() {
	        return (this.getImplementationsByType('PCBLIB').find((impl) => impl.isCurrent) ??
	            this.getImplementationsByType('PCBLIB')[0]);
	    }
	}
	AltiumImplementationList.AltiumImplementationList = AltiumImplementationList$1;
	
	return AltiumImplementationList;
}

var AltiumSheetSymbol = {};

var hasRequiredAltiumSheetSymbol;

function requireAltiumSheetSymbol () {
	if (hasRequiredAltiumSheetSymbol) return AltiumSheetSymbol;
	hasRequiredAltiumSheetSymbol = 1;
	Object.defineProperty(AltiumSheetSymbol, "__esModule", { value: true });
	AltiumSheetSymbol.AltiumSheetSymbol = void 0;
	AltiumSheetSymbol.withDefaultSchDocExtension = withDefaultSchDocExtension;
	const SchDocTypes_1 = requireSchDocTypes();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	function withDefaultSchDocExtension(reference) {
	    if (!reference || reference.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.SchDoc.toLowerCase())) {
	        return reference;
	    }
	    return `${reference}${altiumFileTypes_1.AltiumFileType.SchDoc}`;
	}
	class AltiumSheetSymbol$1 {
	    constructor(schematic, record) {
	        this.schematic = schematic;
	        this.record = record;
	    }
	    get uniqueId() {
	        return this.record.uniqueId;
	    }
	    getEntries() {
	        if (!this._entries) {
	            const children = this.schematic.getChildRecords(this.record.index);
	            this._entries = children.filter((r) => r.recordType === SchDocTypes_1.RecordType.SHEET_ENTRY);
	        }
	        return this._entries;
	    }
	    getSheetName() {
	        if (!this._sheetName) {
	            const children = this.schematic.getChildRecords(this.record.index);
	            const record = children.find((r) => r.recordType === SchDocTypes_1.RecordType.SHEET_NAME);
	            if (!record) {
	                throw new Error(`Sheet symbol ${this.record.index} has no sheet name`);
	            }
	            this._sheetName = record;
	        }
	        return this._sheetName;
	    }
	    getFileName() {
	        if (!this._fileName) {
	            const children = this.schematic.getChildRecords(this.record.index);
	            const record = children.find((r) => r.recordType === SchDocTypes_1.RecordType.FILE_NAME);
	            if (!record) {
	                throw new Error(`Sheet symbol ${this.record.index} has no file name`);
	            }
	            this._fileName = record;
	        }
	        return this._fileName;
	    }
	    getSheetNameText() {
	        return this.getSheetName().text;
	    }
	    getFileNameText() {
	        return this.getFileName().text;
	    }
	    getEntryByName(name) {
	        return this.getEntries().find((e) => e.name === name);
	    }
	}
	AltiumSheetSymbol.AltiumSheetSymbol = AltiumSheetSymbol$1;
	
	return AltiumSheetSymbol;
}

var AltiumSheet = {};

var CoordinateConverter = {};

var hasRequiredCoordinateConverter;

function requireCoordinateConverter () {
	if (hasRequiredCoordinateConverter) return CoordinateConverter;
	hasRequiredCoordinateConverter = 1;
	Object.defineProperty(CoordinateConverter, "__esModule", { value: true });
	CoordinateConverter.CoordinateConverter = CoordinateConverter.AltiumCoordinateSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	CoordinateConverter.AltiumCoordinateSchema = zod_1.z
	    .object({
	    x: zod_1.z.number(),
	    y: zod_1.z.number(),
	})
	    .strict();
	class CoordinateConverter$1 {
	    static convertCoordinate(x, xFrac = 0, y, yFrac = 0, invertY = true) {
	        const finalX = x + xFrac / 100000;
	        let finalY = y + yFrac / 100000;
	        if (invertY) {
	            finalY = -finalY;
	        }
	        return { x: finalX * 10, y: finalY * 10 };
	    }
	    static convertCoordinateNumber(value, valueFrac = 0, valueFrac1 = 0) {
	        return (value + valueFrac / 100000 + valueFrac1 / 1000000) * 10;
	    }
	    static parseFractionalValue(properties, key) {
	        return (parseInt(properties[key]?.value ?? '0', 10) +
	            parseInt(properties[`${key}_frac`]?.value ?? '0', 10) / 100000 +
	            parseInt(properties[`${key}_frac1`]?.value ?? '0', 10) / 1000000);
	    }
	    static parseFractionalCoordinate(properties, key) {
	        return CoordinateConverter$1.parseFractionalValue(properties, key) * 10;
	    }
	    static parseFractionalPoint(properties, xKey, yKey, invertY = true) {
	        const x = CoordinateConverter$1.parseFractionalValue(properties, xKey);
	        const y = CoordinateConverter$1.parseFractionalValue(properties, yKey);
	        return { x: x * 10, y: (invertY ? -y : y) * 10 };
	    }
	    static convertObjectLocation(properties) {
	        return CoordinateConverter$1.parseFractionalPoint(properties, 'location.x', 'location.y');
	    }
	    static convertColor(altiumColor) {
	        const r = altiumColor & 0xff;
	        const g = (altiumColor >> 8) & 0xff;
	        const b = (altiumColor >> 16) & 0xff;
	        return { r, g, b };
	    }
	    static rgbToAltiumColor(r, g, b) {
	        return (b << 16) | (g << 8) | r;
	    }
	    static milsToMm(mils) {
	        return mils * 0.0254;
	    }
	    static mmToMils(mm) {
	        return mm / 0.0254;
	    }
	    static altiumBool(value) {
	        return value === 'T' || value === 'TRUE' || value === '1' || value === 'ON' || value === 'YES';
	    }
	    static convertJustification(justification) {
	        const horizontal = justification % 3;
	        const vertical = Math.floor(justification / 3);
	        return [horizontal - 1, 1 - vertical];
	    }
	}
	CoordinateConverter.CoordinateConverter = CoordinateConverter$1;
	
	return CoordinateConverter;
}

var fonts = {};

var newstroke = {};

var hasRequiredNewstroke;

function requireNewstroke () {
	if (hasRequiredNewstroke) return newstroke;
	hasRequiredNewstroke = 1;
	Object.defineProperty(newstroke, "__esModule", { value: true });
	newstroke.newstroke_font = void 0;
	newstroke.newstroke_font = [
	    'JZ',
	    'MWRYSZR[QZRYR[ RRSQGRFSGRSRF',
	    'JZNFNJ RVFVJ',
	    'H]LM[M RRDL_ RYVJV RS_YD',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RRCR^',
	    'F^J[ZF RMFOGPIOKMLKKJIKGMF RYZZXYVWUUVTXUZW[YZ',
	    'E_[[Z[XZUWPQNNMKMINGPFQFSGTITJSLRMLQKRJTJWKYLZN[Q[SZTYWUXRXP',
	    'MWSFQJ',
	    'KYVcUbS_R]QZPUPQQLRISGUDVC',
	    'KYNcObQ_R]SZTUTQSLRIQGODNC',
	    'JZRFRK RMIRKWI ROORKUO',
	    'E_JSZS RR[RK',
	    'MWSZS[R]Q^',
	    'E_JSZS',
	    'MWRYSZR[QZRYR[',
	    'G][EI`',
	    'H\\QFSFUGVHWJXNXSWWVYUZS[Q[OZNYMWLSLNMJNHOGQF',
	    'H\\X[L[ RR[RFPINKLL',
	    'H\\LHMGOFTFVGWHXJXLWOK[X[',
	    'H\\KFXFQNTNVOWPXRXWWYVZT[N[LZKY',
	    'H\\VMV[ RQELTYT',
	    'H\\WFMFLPMOONTNVOWPXRXWWYVZT[O[MZLY',
	    'H\\VFRFPGOHMKLOLWMYNZP[T[VZWYXWXRWPVOTNPNNOMPLR',
	    'H\\KFYFP[',
	    'H\\PONNMMLKLJMHNGPFTFVGWHXJXKWMVNTOPONPMQLSLWMYNZP[T[VZWYXWXSWQVPTO',
	    'H\\N[R[TZUYWVXRXJWHVGTFPFNGMHLJLOMQNRPSTSVRWQXO',
	    'MWRYSZR[QZRYR[ RRNSORPQORNRP',
	    'MWSZS[R]Q^ RRNSORPQORNRP',
	    'E_ZMJSZY',
	    'E_JPZP RZVJV',
	    'E_JMZSJY',
	    'I[QYRZQ[PZQYQ[ RMGOFTFVGWIWKVMUNSORPQRQS',
	    'D_VQUPSOQOOPNQMSMUNWOXQYSYUXVW RVOVWWXXXZW[U[PYMVKRJNKKMIPHTIXK[N]R^V]Y[',
	    'I[MUWU RK[RFY[',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[',
	    'H[MPTP RW[M[MFWF',
	    'HZTPMP RM[MFWF',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR',
	    'G]L[LF RLPXP RX[XF',
	    'MWR[RF',
	    'JZUFUUTXRZO[M[',
	    'G\\L[LF RX[OO RXFLR',
	    'HYW[M[MF',
	    'F^K[KFRUYFY[',
	    'G]L[LFX[XF',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ',
	    'G]Z]X\\VZSWQVOV RP[NZLXKTKMLINGPFTFVGXIYMYTXXVZT[P[',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG',
	    'JZLFXF RR[RF',
	    'G]LFLWMYNZP[T[VZWYXWXF',
	    'I[KFR[YF',
	    'F^IFN[RLV[[F',
	    'H\\KFY[ RYFK[',
	    'I[RQR[ RKFRQYF',
	    'H\\KFYFK[Y[',
	    'KYVbQbQDVD',
	    'KYID[_',
	    'KYNbSbSDND',
	    'LXNHREVH',
	    'JZJ]Z]',
	    'NVPESH',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR',
	    'H[M[MF RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT',
	    'MYOMWM RR[RISGUFWF',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN',
	    'H[M[MF RV[VPUNSMPMNNMO',
	    'MWR[RM RRFQGRHSGRFRH',
	    'MWRMR_QaObNb RRFQGRHSGRFRH',
	    'IZN[NF RPSV[ RVMNU',
	    'MXU[SZRXRF',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[',
	    'I\\NMN[ RNOONQMTMVNWPW[',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'I\\WMWb RWZU[Q[OZNYMWMQNOONQMUMWN',
	    'KXP[PM RPQQORNTMVM',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN',
	    'MYOMWM RRFRXSZU[W[',
	    'H[VMV[ RMMMXNZP[S[UZVY',
	    'JZMMR[WM',
	    'G]JMN[RQV[ZM',
	    'IZL[WM RLMW[',
	    'JZMMR[ RWMR[P`OaMb',
	    'IZLMWML[W[',
	    'KYVcUcSbR`RVQTOSQRRPRFSDUCVC',
	    'H\\RbRD',
	    'KYNcOcQbR`RVSTUSSRRPRFQDOCNC',
	    'KZMSNRPQTSVRWQ',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'JZ',
	    'MWROQNRMSNRORM RRUSaRbQaRURb',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RRJR^',
	    'H[LMTM RL[W[ RO[OIPGRFUFWG',
	    'H]LYOV RLLOO RVVYY RVOYL RVVTWQWOVNTNQOOQNTNVOWQWTVV',
	    'F^JTZT RJMZM RRQR[ RKFRQYF',
	    'MWRbRW RRFRQ',
	    'I[N]P^S^U]V[UYOSNQNPONQM RVGTFQFOGNIOKUQVSVTUVSW',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH',
	    '@dVKTJPJNKLMKOKSLUNWPXTXVW RRCMDHGELDQEVH[M^R_W^\\[_V`Q_L\\GWDRC',
	    'KZOEQDSDUEVGVN RVMTNQNOMNKOIQHVH',
	    'H\\RMLSRY RXWTSXO',
	    'E_JQZQZV',
	    'RR',
	    '@dWXRR RNXNJTJVKWMWOVQTRNR RRCMDHGELDQEVH[M^R_W^\\[_V`Q_L\\GWDRC',
	    'LXMGWG',
	    'JZRFPGOIPKRLTKUITGRF',
	    'E_JOZO RRWRG RZ[J[',
	    'JZNAP@S@UAVCVEUGNNVN',
	    'JZN@V@RESEUFVHVKUMSNPNNM',
	    'NVTEQH',
	    'H^MMMb RWXXZZ[ RMXNZP[T[VZWXWM',
	    'F]VMV[ ROMOXNZL[ RZMMMKNJP',
	    'JZRRQSRTSSRRRT',
	    'MWR\\T]U_TaRbOb',
	    'JZVNNN RNCPBR@RN',
	    'KYQNOMNKNGOEQDSDUEVGVKUMSNQN',
	    'H\\RMXSRY RLWPSLO',
	    'G]KQYQ RVNNN RNCPBR@RN RUYUa RQSN]W]',
	    'G]KQYQ RVNNN RNCPBR@RN RNTPSSSUTVVVXUZNaVa',
	    'G]KQYQ RN@V@RESEUFVHVKUMSNPNNM RUYUa RQSN]W]',
	    'I[SORNSMTNSOSM RWaUbPbNaM_M]N[OZQYRXSVSU',
	    'I[MUWU RK[RFY[ RP>SA',
	    'I[MUWU RK[RFY[ RT>QA',
	    'I[MUWU RK[RFY[ RNAR>VA',
	    'I[MUWU RK[RFY[ RMAN@P?TAV@W?',
	    'I[MUWU RK[RFY[ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'I[MUWU RK[RFY[ RRFPEOCPAR@TAUCTERF',
	    'F`JURU RRPYP RH[OF\\F RRFR[\\[',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RR\\T]U_TaRbOb',
	    'H[MPTP RW[M[MFWF RP>SA',
	    'H[MPTP RW[M[MFWF RT>QA',
	    'H[MPTP RW[M[MFWF RNAR>VA',
	    'H[MPTP RW[M[MFWF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'MWR[RF RP>SA',
	    'MWR[RF RT>QA',
	    'MWR[RF RNAR>VA',
	    'MWR[RF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RIPQP',
	    'G]L[LFX[XF RMAN@P?TAV@W?',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RP>SA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RT>QA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RNAR>VA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RMAN@P?TAV@W?',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'E_LMXY RXMLY',
	    'G]ZFJ[ RP[NZLXKTKMLINGPFTFVGXIYMYTXXVZT[P[',
	    'G]LFLWMYNZP[T[VZWYXWXF RP>SA',
	    'G]LFLWMYNZP[T[VZWYXWXF RT>QA',
	    'G]LFLWMYNZP[T[VZWYXWXF RNAR>VA',
	    'G]LFLWMYNZP[T[VZWYXWXF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'I[RQR[ RKFRQYF RT>QA',
	    'G\\LFL[ RLKTKVLWMXOXRWTVUTVLV',
	    'F]K[KJLHMGOFRFTGUHVJVMSMQNPPPQQSSTVTXUYWYXXZV[R[PZ',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RPESH',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RTEQH',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNHREVH',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RMHNGPFTHVGWF',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RRHPGOEPCRBTCUETGRH',
	    'D`INKMOMQNRP R[ZY[U[SZRXRPSNUMYM[N\\P\\RRSKSITHVHXIZK[O[QZRX',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RR\\T]U_TaRbOb',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RPESH',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RTEQH',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNHREVH',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'MWR[RM RPESH',
	    'MWR[RM RTEQH',
	    'LXNHREVH RR[RM',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH RR[RM',
	    'I\\SCQI RWNUMQMONNOMQMXNZP[T[VZWXWLVITGRFNE',
	    'I\\NMN[ RNOONQMTMVNWPW[ RMHNGPFTHVGWF',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RPESH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEQH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNHREVH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMHNGPFTHVGWF',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'E_ZSJS RRXSYRZQYRXRZ RRLSMRNQMRLRN',
	    'H[XMK[ RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'H[VMV[ RMMMXNZP[S[UZVY RPESH',
	    'H[VMV[ RMMMXNZP[S[UZVY RTEQH',
	    'H[VMV[ RMMMXNZP[S[UZVY RNHREVH',
	    'H[VMV[ RMMMXNZP[S[UZVY RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'JZMMR[ RWMR[P`OaMb RTEQH',
	    'H[MFMb RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'JZMMR[ RWMR[P`OaMb RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I[MUWU RK[RFY[ RM@W@',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RMGWG',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V>',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE',
	    'I[MUWU RK[RFY[ RY[W]V_WaYb[b',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RW[U]T_UaWbYb',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RT>QA',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RTEQH',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RNAR>VA',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RNHREVH',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RR?Q@RAS@R?RA',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RRFQGRHSGRFRH',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RN>RAV>',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RNERHVE',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RN>RAV>',
	    'IfW[WF RWZU[Q[OZNYMWMQNOONQMUMWN RbF`J',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RIPQP',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RRHZH',
	    'H[MPTP RW[M[MFWF RM@W@',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RMGWG',
	    'H[MPTP RW[M[MFWF RN>O@QASAU@V>',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNEOGQHSHUGVE',
	    'H[MPTP RW[M[MFWF RR?Q@RAS@R?RA',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RRFQGRHSGRFRH',
	    'H[MPTP RW[M[MFWF RR[P]O_PaRbTb',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RR[P]O_PaRbTb',
	    'H[MPTP RW[M[MFWF RN>RAV>',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNERHVE',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RNAR>VA',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RNHREVH',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RN>O@QASAU@V>',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RNEOGQHSHUGVE',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RR?Q@RAS@R?RA',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RRFQGRHSGRFRH',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RR\\T]U_TaRbOb',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RRGPFODPBRAUA',
	    'G]L[LF RLPXP RX[XF RNAR>VA',
	    'H[M[MF RV[VPUNSMPMNNMO RIAM>QA',
	    'G]IJ[J RL[LF RLPXP RX[XF',
	    'H[M[MF RV[VPUNSMPMNNMO RJHRH',
	    'MWR[RF RMAN@P?TAV@W?',
	    'MWR[RM RMHNGPFTHVGWF',
	    'MWR[RF RM@W@',
	    'MWR[RM RMGWG',
	    'MWR[RF RN>O@QASAU@V>',
	    'MWR[RM RNEOGQHSHUGVE',
	    'MWR[RF RR[P]O_PaRbTb',
	    'MWR[RM RR[P]O_PaRbTb',
	    'MWR[RF RR?Q@RAS@R?RA',
	    'MWR[RM',
	    'MgR[RF RbFbUaX_Z\\[Z[',
	    'MaR[RM RRFQGRHSGRFRH R\\M\\_[aYbXb R\\F[G\\H]G\\F\\H',
	    'JZUFUUTXRZO[M[ RQAU>YA',
	    'MWRMR_QaObNb RNHREVH',
	    'G\\L[LF RX[OO RXFLR RR\\T]U_TaRbOb',
	    'IZN[NF RPSV[ RVMNU RR\\T]U_TaRbOb',
	    'IZNMN[ RPSV[ RVMNU',
	    'HYW[M[MF RO>LA',
	    'MXU[SZRXRF RTEQH',
	    'HYW[M[MF RR\\T]U_TaRbOb',
	    'MXU[SZRXRF RR\\T]U_TaRbOb',
	    'HYW[M[MF RVHSK',
	    'M^U[SZRXRF RZFXJ',
	    'HYW[M[MF RUNTOUPVOUNUP',
	    'M\\U[SZRXRF RYOZPYQXPYOYQ',
	    'HYW[M[MF RJQPM',
	    'MXU[SZRXRF ROQUM',
	    'G]L[LFX[XF RT>QA',
	    'I\\NMN[ RNOONQMTMVNWPW[ RTEQH',
	    'G]L[LFX[XF RR\\T]U_TaRbOb',
	    'I\\NMN[ RNOONQMTMVNWPW[ RR\\T]U_TaRbOb',
	    'G]L[LFX[XF RN>RAV>',
	    'I\\NMN[ RNOONQMTMVNWPW[ RNERHVE',
	    'MjSFQJ R\\M\\[ R\\O]N_MbMdNePe[',
	    'G]LFL[ RLINGPFTFVGWHXJX^W`VaTbQb',
	    'I\\NMN[ RNOONQMTMVNWPW_VaTbRb',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RM@W@',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMGWG',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RN>O@QASAU@V>',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNEOGQHSHUGVE',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RQ>NA RX>UA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RQENH RXEUH',
	    'E`RPYP RRFR[ R\\FNFLGJIIMITJXLZN[\\[',
	    'C`[ZY[U[SZRXRPSNUMYM[N\\P\\RRT RRQQOPNNMKMINHOGQGWHYIZK[N[PZQYRW',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RT>QA',
	    'KXP[PM RPQQORNTMVM RTEQH',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RR\\T]U_TaRbOb',
	    'KXP[PM RPQQORNTMVM RR\\T]U_TaRbOb',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RN>RAV>',
	    'KXP[PM RPQQORNTMVM RNERHVE',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RT>QA',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RTEQH',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RNAR>VA',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RNHREVH',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RR\\T]U_TaRbOb',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RR\\T]U_TaRbOb',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RN>RAV>',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RNERHVE',
	    'JZLFXF RR[RF RR\\T]U_TaRbOb',
	    'MYOMWM RRFRXSZU[W[ RR\\T]U_TaRbOb',
	    'JZLFXF RR[RF RN>RAV>',
	    'M[OMWM RYFXI RRFRXSZU[W[',
	    'JZLFXF RR[RF RNQVQ',
	    'MYOMWM RRFRXSZU[W[ ROSUS',
	    'G]LFLWMYNZP[T[VZWYXWXF RMAN@P?TAV@W?',
	    'H[VMV[ RMMMXNZP[S[UZVY RMHNGPFTHVGWF',
	    'G]LFLWMYNZP[T[VZWYXWXF RM@W@',
	    'H[VMV[ RMMMXNZP[S[UZVY RMGWG',
	    'G]LFLWMYNZP[T[VZWYXWXF RN>O@QASAU@V>',
	    'H[VMV[ RMMMXNZP[S[UZVY RNEOGQHSHUGVE',
	    'G]LFLWMYNZP[T[VZWYXWXF RRAP@O>P<R;T<U>T@RA',
	    'H[VMV[ RMMMXNZP[S[UZVY RRHPGOEPCRBTCUETGRH',
	    'G]LFLWMYNZP[T[VZWYXWXF RQ>NA RX>UA',
	    'H[VMV[ RMMMXNZP[S[UZVY RQENH RXEUH',
	    'G]LFLWMYNZP[T[VZWYXWXF RR[P]O_PaRbTb',
	    'H[VMV[ RMMMXNZP[S[UZVY RV[T]S_TaVbXb',
	    'F^IFN[RLV[[F RNAR>VA',
	    'G]JMN[RQV[ZM RNHREVH',
	    'I[RQR[ RKFRQYF RNAR>VA',
	    'JZMMR[ RWMR[P`OaMb RNHREVH',
	    'JZLFXF RR[RF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'H\\KFYFK[Y[ RT>QA',
	    'IZLMWML[W[ RTEQH',
	    'H\\KFYFK[Y[ RR?Q@RAS@R?RA',
	    'IZLMWML[W[ RRFQGRHSGRFRH',
	    'H\\KFYFK[Y[ RN>RAV>',
	    'IZLMWML[W[ RNERHVE',
	    'MYR[RISGUFWF',
	    'H[M[MF RMNOMSMUNVOWQWWVYUZS[O[MZ RJHRH',
	    'C\\LFL[T[VZWYXWXTWRVQSPLP RFKFIGGIFSFUGVHWJWLVNUOSP',
	    'G\\VFLFL[R[UZWXXVXSWQUORNLN',
	    'H[WFMFM[ RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'H]MFM[S[VZXXYVYSXQVOSNMN',
	    'IZNMN[S[UZVXVUUSSRNR',
	    'I^MHNGQFSFVGXIYKZOZRYVXXVZS[Q[NZMY',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RMHKGJEKCLB',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RTMTIUGWFYF',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RIPQP',
	    'C\\FKFIGGIFQFTGVIWKXOXRWVVXTZQ[L[LF',
	    'H]NFXFX[R[OZMXLVLSMQOORNXN',
	    'I\\MFWFW[ RWNUMQMONNOMQMWNYOZQ[U[WZ',
	    'I\\Q[T[VZWYXWXQWOVNTMQMONNOMQMWNYOZQ[T\\V]W_VaTbPbNa',
	    'I\\WPPP RM[W[WFMF',
	    'F^MHNGQFSFVGXIYKZOZRYVXXVZS[Q[NZLXKVJRZP',
	    'G[PPTP RWGUFPFNGMHLJLLMNNOPPMQLRKTKWLYMZO[U[WZ',
	    'HZTPMP RM[MFWF RM[M_LaJbHb',
	    'MYOMWM RR[RISGUFWF RR[R_QaObMb',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RMHKGJEKCLB',
	    'I[KFU[U_TaRbPaO_O[YF',
	    'D`I[IF RIOJNLMOMQNRPRXSZU[X[ZZ[Y\\W\\P[NZM',
	    'MZRFRWSYTZV[X[',
	    'MWR[RF RNPVP',
	    'G_L[LF RX[OO RLRWGYF[G\\I\\K',
	    'IZNMN[ RPSV[ RVMNU RNMNIOGQFSF',
	    'MXU[SZRXRF RNOVO',
	    'JZRMM[ RMFOFPGRMW[ RNLTH',
	    'Ca\\F\\[ R\\XZZX[V[TZSYRWRF RRWQYPZN[L[JZIYHWHF',
	    'G]L[LFX[XF RL[L_KaIbGb',
	    'I\\NMN[ RNOONQMTMVNWPWb',
	    'G]KPYP RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RVGXFYDXBWA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUNWMXKWIVH',
	    'DaSGQFMFKGIIHMHTIXKZM[Q[SZUXVTVMUISGUFYF[G\\I\\b',
	    'E^RNPMMMKNJOIQIWJYKZM[P[RZSYTWTQSORNTMVMXNYPYb',
	    'C\\LFL[ RFKFIGGIFTFVGWHXJXMWOVPTQLQ',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ RRMRISGUFWF',
	    'G\\LFL[ RQVXb RLKTKVLWMXOXRWTVUTVLV',
	    'H\\XZU[P[NZMYLWLUMSNRPQTPVOWNXLXJWHVGTFOFLG',
	    'IZVZT[P[NZMXMWNUPTSTUSVQVPUNSMPMNN',
	    'H[W[L[SPLFWF',
	    'JYWbUbSaR_RIQGOFMGLIMKOLQKRI',
	    'MYOMWM RRFRXSZU[W[ RW[W_VaTbRb',
	    'HZR[RF RKKKILGNFXF',
	    'MYOMWM RWFUFSGRIRXSZU[W[',
	    'JZLFXF RR[RF RR[R_SaUbWb',
	    'G]LFLWMYNZP[T[VZWYXWXF RXFZE[CZAY@',
	    'H[VMV[ RMMMXNZP[S[UZVY RVMXLYJXHWG',
	    'F^ZFUFUJWKYMZPZUYXWZT[P[MZKXJUJPKMMKOJOFJF',
	    'G]LFLWMYNZP[T[VZXXYVYIXGWF',
	    'I`RQR[ RKFRQXGZF\\G]I]K',
	    'J^MMR[ RMbOaP`R[VNXMZN[P[R',
	    'H\\KFYFK[Y[ RNPVP',
	    'IZLMWML[W[ RNTVT',
	    'H\\KFXFQNTNVOWPXRXWWYVZT[N[LZKY',
	    'H\\YFLFSNPNNOMPLRLWMYNZP[V[XZYY',
	    'JZWMNMUVRVPWOXNZN^O`PaRbUbWa',
	    'JZMMVMOTSTUUVWVXUZS[Q[O\\N^N_OaQbVb',
	    'H\\LHMGOFTFVGWHXJXLWOK[X[ RNSVS',
	    'H\\WFMFLPMOONTNVOWPXRXWWYVZT[O[MZLY',
	    'JZVMOMNSPRSRUSVUVXUZS[P[NZ',
	    'J^MZP[T[WZYXZVZSYQWOTNPNPF RLITI',
	    'H[MMMb RMONNPMTMVNWPWSVUM^',
	    'MWRFRb',
	    'JZOFOb RUFUb',
	    'MWRFRb ROWUW ROQUQ',
	    'MWRYSZR[QZRYR[ RRSQGRFSGRSRF',
	    'GpL[LFQFTGVIWKXOXRWVVXTZQ[L[ R_FmF_[m[ Rb>fAj>',
	    'GmL[LFQFTGVIWKXOXRWVVXTZQ[L[ R_MjM_[j[ RaEeHiE',
	    'ImW[WF RWZU[Q[OZNYMWMQNOONQMUMWN R_MjM_[j[ RaEeHiE',
	    'HiW[M[MF RdFdUcXaZ^[\\[',
	    'HcW[M[MF R^M^_]a[bZb R^F]G^H_G^F^H',
	    'MbU[SZRXRF R]M]_\\aZbYb R]F\\G]H^G]F]H',
	    'GmL[LFX[XF RhFhUgXeZb[`[',
	    'GgL[LFX[XF RbMb_aa_b^b RbFaGbHcGbFbH',
	    'IfNMN[ RNOONQMTMVNWPW[ RaMa_`a^b]b RaF`GaHbGaFaH',
	    'I[MUWU RK[RFY[ RN>RAV>',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNERHVE',
	    'MWR[RF RN>RAV>',
	    'MWR[RM RNERHVE',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RN>RAV>',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNERHVE',
	    'G]LFLWMYNZP[T[VZWYXWXF RN>RAV>',
	    'H[VMV[ RMMMXNZP[S[UZVY RNERHVE',
	    'G]LFLWMYNZP[T[VZWYXWXF RN?O@NAM@N?NA RV?W@VAU@V?VA RM;W;',
	    'H[VMV[ RMMMXNZP[S[UZVY RNFOGNHMGNFNH RVFWGVHUGVFVH RM@W@',
	    'G]LFLWMYNZP[T[VZWYXWXF RN?O@NAM@N?NA RV?W@VAU@V?VA RT9Q<',
	    'H[VMV[ RMMMXNZP[S[UZVY RNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'G]LFLWMYNZP[T[VZWYXWXF RN?O@NAM@N?NA RV?W@VAU@V?VA RN9R<V9',
	    'H[VMV[ RMMMXNZP[S[UZVY RNFOGNHMGNFNH RVFWGVHUGVFVH RN>RAV>',
	    'G]LFLWMYNZP[T[VZWYXWXF RN?O@NAM@N?NA RV?W@VAU@V?VA RP9S<',
	    'H[VMV[ RMMMXNZP[S[UZVY RNFOGNHMGNFNH RVFWGVHUGVFVH RP>SA',
	    'I[NNPMTMVNWPWXVZT[P[NZMXMVWT',
	    'I[MUWU RK[RFY[ RN?O@NAM@N?NA RV?W@VAU@V?VA RM;W;',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNFOGNHMGNFNH RVFWGVHUGVFVH RM@W@',
	    'I[MUWU RK[RFY[ RR?Q@RAS@R?RA RM;W;',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RRFQGRHSGRFRH RM@W@',
	    'F`JURU RRPYP RH[OF\\F RRFR[\\[ RO@Y@',
	    'D`INKMOMQNRP R[ZY[U[SZRXRPSNUMYM[N\\P\\RRSKSITHVHXIZK[O[QZRX RMGWG',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RSV[V',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RS^[^',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RN>RAV>',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RNERHVE',
	    'G\\L[LF RX[OO RXFLR RN>RAV>',
	    'IZN[NF RPSV[ RVMNU RJANDRA',
	    'G]R[P]O_PaRbTb RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'H[R[P]O_PaRbTb RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'G]R[P]O_PaRbTb RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RM@W@',
	    'H[R[P]O_PaRbTb RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMGWG',
	    'H\\KFXFQNTNVOWPXRXWWYVZT[N[LZKY RN>RAV>',
	    'JZMMVMOVRVTWUXVZV^U`TaRbObMa RNERHVE',
	    'MWRMR_QaObNb RNERHVE',
	    'GpL[LFQFTGVIWKXOXRWVVXTZQ[L[ R_FmF_[m[',
	    'GmL[LFQFTGVIWKXOXRWVVXTZQ[L[ R_MjM_[j[',
	    'ImW[WF RWZU[Q[OZNYMWMQNOONQMUMWN R_MjM_[j[',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RT>QA',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RTEQH',
	    'CaH[HF RHPTP RTFTXUZW[Z[\\Z]X]M',
	    'G\\LFLb RLINGPFTFVGWHXJXOWRUUL^',
	    'G]L[LFX[XF RP>SA',
	    'I\\NMN[ RNOONQMTMVNWPW[ RPESH',
	    'I[MUWU RK[RFY[ RZ9X< RR;P<O>P@RAT@U>T<R;',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RZ@XC RRBPCOEPGRHTGUETCRB',
	    'F`JURU RRPYP RH[OF\\F RRFR[\\[ RV>SA',
	    'D`INKMOMQNRP R[ZY[U[SZRXRPSNUMYM[N\\P\\RRSKSITHVHXIZK[O[QZRX RTEQH',
	    'G]ZFJ[ RP[NZLXKTKMLINGPFTFVGXIYMYTXXVZT[P[ RT>QA',
	    'H[XMK[ RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEQH',
	    'I[MUWU RK[RFY[ ROAL> RVAS>',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR ROHLE RVHSE',
	    'I[MUWU RK[RFY[ RNAO?Q>S>U?VA',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNHOFQESEUFVH',
	    'H[MPTP RW[M[MFWF ROAL> RVAS>',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT ROHLE RVHSE',
	    'H[MPTP RW[M[MFWF RNAO?Q>S>U?VA',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNHOFQESEUFVH',
	    'MWR[RF ROAL> RVAS>',
	    'MWR[RM ROHLE RVHSE',
	    'MWR[RF RNAO?Q>S>U?VA',
	    'MWR[RM RNHOFQESEUFVH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF ROAL> RVAS>',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ ROHLE RVHSE',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RNAO?Q>S>U?VA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNHOFQESEUFVH',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ ROAL> RVAS>',
	    'KXP[PM RPQQORNTMVM RPHME RWHTE',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RNAO?Q>S>U?VA',
	    'KXP[PM RPQQORNTMVM ROHPFRETEVFWH',
	    'G]LFLWMYNZP[T[VZWYXWXF ROAL> RVAS>',
	    'H[VMV[ RMMMXNZP[S[UZVY ROHLE RVHSE',
	    'G]LFLWMYNZP[T[VZWYXWXF RNAO?Q>S>U?VA',
	    'H[VMV[ RMMMXNZP[S[UZVY RNHOFQESEUFVH',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RS`SaRcQd',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RS`SaRcQd',
	    'JZLFXF RR[RF RS`SaRcQd',
	    'MYOMWM RRFRXSZU[W[ RU`UaTcSd',
	    'I]VRXTYVY[X]V_T`Lb RLHMGOFUFWGXHYJYNXPVRTSNU',
	    'J[UWVXWZW]V_U`SaMb RMNOMSMUNVOWQWTVVUWSXOY',
	    'G]L[LF RLPXP RX[XF RN>RAV>',
	    'H[M[MF RV[VPUNSMPMNNMO RI>MAQ>',
	    'G]L[LFX[XF RX[Xb',
	    'IbWFWXXZZ[\\[^Z_X^V\\UZVV^ RWNUMQMONNOMQMWNYOZQ[T[VZWX',
	    'G]NFLGKIKKLMMNOO RVFXGYIYKXMWNUO ROOUOWPXQYSYWXYWZU[O[MZLYKWKSLQMPOO',
	    'J[MJMMNORQVOWMWJ RPQTQVRWTWXVZT[P[NZMXMTNRPQ',
	    'H\\KFYFK[Y[ RY[Y_XaVbTb',
	    'IZLMWML[W[ RW[W_VaTbRb',
	    'I[MUWU RK[RFY[ RR?Q@RAS@R?RA',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RRFQGRHSGRFRH',
	    'H[MPTP RW[M[MFWF RR\\T]U_TaRbOb',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RR\\T]U_TaRbOb',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RN?O@NAM@N?NA RV?W@VAU@V?VA RM;W;',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNFOGNHMGNFNH RVFWGVHUGVFVH RM@W@',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RMAN@P?TAV@W? RM;W;',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMHNGPFTHVGWF RM@W@',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RR?Q@RAS@R?RA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RRFQGRHSGRFRH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RR?Q@RAS@R?RA RM;W;',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RRFQGRHSGRFRH RM@W@',
	    'I[RQR[ RKFRQYF RM@W@',
	    'JZMMR[ RWMR[P`OaMb RMGWG',
	    'M]RFRXSZU[W[YZZXYVWUUVQ^',
	    'IbNMN[ RNOONQMTMVNWPWXXZZ[\\[^Z_X^V\\UZVV^',
	    'M]OMWM RRFRXSZU[W[YZZXYVWUUVQ^',
	    'MWRMR_QaObNb',
	    'D`R[RF RRZP[L[JZIYHWHQIOJNLMPMRN RTMXMZN[O\\Q\\W[YZZX[T[RZ',
	    'D`RMRb RRZP[L[JZIYHWHQIOJNLMPMRN RTMXMZN[O\\Q\\W[YZZX[T[RZ',
	    'I[MUWU RK[RFY[ RXCL`',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RXCL`',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RWHM`',
	    'HYW[M[MF RIOQO',
	    'JZLFXF RR[RF RXCL`',
	    'J[P[R^T_W_ RNZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN',
	    'IZLMWML[N[P\\R^T_W_',
	    'J^MGPFTFWGYIZKZNYPWRTSPSP[',
	    'J^NNPMTMVNWOXQXSWUVVTWPWP[',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP RIUOU',
	    'G]IM[M RLFLWMYNZP[T[VZWYXWXF',
	    'I[Y[RFK[',
	    'H[MPTP RW[M[MFWF RXCL`',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RWHM`',
	    'JZUFUUTXRZO[M[ RQPYP',
	    'MWRMR_QaObNb ROTUT RRFQGRHSGRFRH',
	    'G]XFX^Y`Za\\b^b RXIVGTFPFNGLIKMKTLXNZP[T[VZXX',
	    'I\\WMW^X`Ya[b]b RWZU[Q[OZNYMWMQNOONQMUMWN',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RIQOQ',
	    'KXP[PM RPQQORNTMVM RMTUT',
	    'I[KIYI RRQR[ RKFRQYF',
	    'JZLQXQ RMMR[ RWMR[P`OaMb',
	    'H[MMMXNZP[T[VZ RMNOMTMVNWPWRVTTUOUMV',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[',
	    'G\\K[NQOOPNRMTMVNWOXRXVWYVZT[R[PZOYNWMPLNJM',
	    'H[RFPFNGMIM[ RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'J\\NNPMTMVNWOXQXWWYVZT[P[NZ',
	    'HZVNTMPMNNMOLQLWMYNZP[S[UZVXUVSUQVM^',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RW[W_XaZb\\b',
	    'I\\\\FZFXGWIW[ RWZU[Q[OZNYMWMQNOONQMUMWN',
	    'I[NZP[T[VZWXWPVNTMPMNNMPMRWT',
	    'I[NNPMTMVNWPWXVZT[P[NZMXMVWT',
	    'IbNNPMTMVNWPWXVZT[P[NZMXMV\\S\\U]W_X`X',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN',
	    'J[TTVSWQWPVNTMPMNN RRTTTVUWWWXVZT[P[NZ',
	    'JaRTTTVUWWWXVZT[P[NZ RNNPMTMVNWPWQVSTT[S[U\\W^X_X',
	    'H[TTVSWQWPVNTMPMNNMOLRLVMYNZP[T[VZWXWWVUTTRT',
	    'MWRMR_QaObNb ROTUT',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RWMWIXGZF\\F',
	    'I\\WYVZT[P[NZMXMQNOONQMWMW^V`UaSbMb',
	    'HZUNSMPMNNMOLQLWMYNZP[T[VZVUSU',
	    'JZMMU[U_TaRbPaO_O[WM',
	    'JZMMTVUXTZR[PZOXPVWM',
	    'I\\WMWb RNMNXOZQ[T[VZWY',
	    'H[RFPFNGMIM[ RV[VPUNSMPMNNMO',
	    'H[RFPFNGMIM[ RV[VPUNSMPMNNMO RV[V_UaSbQb',
	    'MWR[RM ROTUT RRFQGRHSGRFRH',
	    'MXRMRXSZU[',
	    'MWR[RM RU[O[ RUMOM',
	    'MXU[SZRXRF RMONNPMTOVNWM',
	    'IYU[SZRXRF RRQQOONMOLQMSOTWT',
	    'MXRFR_SaUbWb',
	    'GZLFLXMZO[ RLMVMOVRVTWUXVZV^U`TaRbObMa',
	    'D`[M[[ R[YZZX[U[SZRXRM RRXQZO[L[JZIXIM',
	    'D`[M[[ R[YZZX[U[SZRXRM RRXQZO[L[JZIXIM R[[[b',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[ R[[[_ZaXbVb',
	    'I\\NMN[ RNOONQMTMVNWPW[ RN[N_MaKbIb',
	    'I\\NMN[ RNOONQMTMVNWPW[ RW[W_XaZb\\b',
	    'H[M[MMV[VM',
	    'H[LTWT RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'E]RTXT RRMR[ RZMMMKNJOIQIWJYKZM[Z[',
	    'G]RTRXSZU[V[XZYXYQXOWNUMOMMNLOKQKXLZN[O[QZRX',
	    'G]RFRb RPMTMVNXPYRYVXXVZT[P[NZLXKVKRLPNNPM',
	    'LYTMT[ RTWSYRZP[N[',
	    'LYTMT[ RTWSYRZP[N[ RTMTF',
	    'LYTMT[ RTWSYRZP[N[ RT[T_UaWbYb',
	    'KXP[PM RPQQORNTMVM RP[Pb',
	    'KXP[PM RPQQORNTMVM RP[P_QaSbUb',
	    'KXM[S[ RVMTMRNQOPRP[',
	    'LYW[Q[ RNMPMRNSOTRT[',
	    'I[RUW[ RN[NMTMVNWPWRVTTUNU',
	    'I[RSWM RNMN[T[VZWXWVVTTSNS',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RN[N_OaQbSb',
	    'KYWFUFSGRIR_QaObMb',
	    'MWRMR_QaObNb ROTUT RRMRISGUFWF',
	    'KYMFOFQGRIRXSZU[W[',
	    'KYWFUFSGRIR_QaObMaL_M]O\\V\\',
	    'KWU[M[ RRbRPQNOMMM',
	    'MYOMWM RRFR_SaUbWb',
	    'H[JRYR RVMV[ RMMMXNZP[S[UZVY',
	    'I\\XMUMUPWRXTXWWYVZT[Q[OZNYMWMTNRPPPMMM',
	    'H[MMMXNZP[S[UZVYWWWPVNUM',
	    'JZW[RMM[',
	    'G]Z[VMRWNMJ[',
	    'JZW[RM RM[RMTHUGWF',
	    'KYRTR[ RMMRTWM',
	    'IZLMWML[W[ RW[W_XaZb\\b',
	    'IZLMWML[T[VZWXVVTURVN^',
	    'JZMMVMOVRVTWUXVZV^U`TaRbObMa',
	    'JZMMVMOVRVTWUXVZV^U`TaRbPbNaM_N]P\\R]Uc',
	    'J^MGPFTFWGYIZKZNYPWRTSPSP[',
	    'FZWGTFPFMGKIJKJNKPMRPSTST[',
	    'J^MZP[T[WZYXZVZSYQWOTNPNPF',
	    'F[WHVGSFQFNGLIKKJOJYK]L_NaQbSbVaW`',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RROQPRQSPRORQ',
	    'I[STVUWWWXVZT[N[NMSMUNVPVQUSSTNT',
	    'I\\PTNUMWMXNZP[T[VZWYXVXRWOVNTMPMNNMPMQNSPTRT',
	    'HZUNSMPMNNMOLQLWMYNZP[T[VZVUSU RUMUIVGXFZF',
	    'H[MTVT RMMM[ RVMV[',
	    'LXRMR_QaObMaL_M]O\\V\\ RRFQGRHSGRFRH',
	    'J[VMVb RTUNM RN[VS',
	    'JYOMO[V[',
	    'I\\WMWb RWZU[Q[OZNYMWMQNOONQMUMWN RWMWIXGZF\\F',
	    'J^MGPFTFWGYIZKZNYPWRTSPSP[ RLXTX',
	    'FZWGTFPFMGKIJKJNKPMRPSTST[ RPXXX',
	    'D`R[RF RRM]MR[][ RRZP[L[JZIYHWHQIOJNLMPMRN',
	    'E`RFR[ RRNPMMMKNJOIQIWJYKZM[P[RZ RRM\\MUVXVZW[X\\Z\\^[`ZaXbUbSa',
	    'D`R[RF RRM]MR[Z[\\Z]X\\VZUXVT^ RRZP[L[JZIYHWHQIOJNLMPMRN',
	    'G^IMQM RLFLXMZO[QZS[W[YZZXZWYUWTTTRSQQQPRNTMWMYN',
	    'I[KMTM RNFNXOZQ[T[ RYFWFUGTIT_SaQbOb',
	    'F^HMPM RKFKXLZN[P[RZ RZNXMTMRNQOPQPWQYRZT[W[YZZXYVWUUVQ^',
	    'F]HMPMP[ RK[KILGNFPF RPOQNSMVMXNYPY_XaVbTb',
	    'G^LFLXMZO[QZS[W[YZZXZWYUWTTTRSQQQPRNTMWMYN',
	    'H^MM[MP[ RMFMXNZP[[[',
	    'G]JSN[RUV[ZS RJFNNRHVNZF',
	    'G]XXXSLSLX RXKXFLFLK',
	    'I\\WMWb RNMNXOZQ[T[VZWY RNMNIMGKFIF',
	    'I\\\\bZbXaW_WM RNMNXOZQ[T[VZWY RNMNIMGKFIF',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'H[MFM[ RXPMP',
	    'IZNTVT RNMN[',
	    'G]R[RF RKOKFYFYO',
	    'I[R[RF RMOMFWFWO',
	    'MWSFQJ',
	    'MWS[Q_',
	    'G]LFL[XFX[',
	    'H\\MMM[WMW[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'NVR`RcSdTd',
	    'J\\NZP[T[VZWYXWXQWOVNTMPMNN',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RRSQTRUSTRSRU',
	    'J\\NZP[T[VZWYXWXQWOVNTMPMNN RRSQTRUSTRSRU',
	    'MWSZS[R]Q^ RRNSORPQORNRP',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'NVTEQH',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'G[MUWU RK[RFY[ RMEJH',
	    'JZRRQSRTSSRRRT',
	    'B[MPTP RW[M[MFWF RHEEH',
	    'A]L[LF RLPXP RX[XF RGEDH',
	    'GWR[RF RMEJH',
	    'RR',
	    'B]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RHEEH',
	    'RR',
	    '@[RQR[ RKFRQYF RFECH',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RFECH',
	    'MXRMRXSZU[ RNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'I[MUWU RK[RFY[',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP',
	    'HZM[MFXF',
	    'I[K[RFY[K[',
	    'H[MPTP RW[M[MFWF',
	    'H\\KFYFK[Y[',
	    'G]L[LF RLPXP RX[XF',
	    'F^OPUP RPFTFVGXIYKZNZSYVXXVZT[P[NZLXKVJSJNKKLINGPF',
	    'MWR[RF',
	    'G\\L[LF RX[OO RXFLR',
	    'I[K[RFY[',
	    'F^K[KFRUYFY[',
	    'G]L[LFX[XF',
	    'H[L[W[ RLFWF RUPNP',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'G]L[LFXFX[',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ',
	    'RR',
	    'H[W[L[SPLFWF',
	    'JZLFXF RR[RF',
	    'I[RQR[ RKFRQYF',
	    'G]R[RF RPITIWJYLZNZRYTWVTWPWMVKTJRJNKLMJPI',
	    'H\\KFY[ RYFK[',
	    'G]R[RF RHFJGKIKNLQMROSUSWRXQYNYIZG\\F',
	    'F^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[',
	    'MWR[RF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'I[RQR[ RKFRQYF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEQH',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RTEQH',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEQH',
	    'MXRMRXSZU[ RTEQH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[',
	    'H[SOUPVQWSWWVYUZS[P[NZMY RKbLaM_MINGPFSFUGVIVLUNSOQO',
	    'JZRYRb RLMMMNNRYWM',
	    'H[SMPMNNMOLQLWMYNZP[S[UZVYWWWQVOUNSMPLNKMINGPFTFVG',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN',
	    'HZMFWFPMNPMSMWNYOZQ[S[U\\V^V_UaSbRb',
	    'I\\NMN[ RNOONQMTMVNWPWb',
	    'H[LPWP RPFSFUGVHWKWVVYUZS[P[NZMYLVLKMHNGPF',
	    'MXRMRXSZU[',
	    'IZNMN[ RPSV[ RVMNU',
	    'JZRMM[ RMFOFPGRMW[',
	    'H^MMMb RWXXZZ[ RMXNZP[T[VZWXWM',
	    'J[MMR[WPWOVM',
	    'HZMFWF RQFOGNINLONQOUO RQOOPNQMSMWNYOZQ[S[U\\V^V_UaSbRb',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'F]VMV[ ROMOXNZL[ RZMMMKNJP',
	    'H\\MbMQNOONQMTMVNWOXQXWWYVZT[Q[OZMX',
	    'HZVNTMPMNNMOLQLWMYNZP[S[U\\V^V_UaSb',
	    'H\\YMPMNNMOLQLWMYNZP[S[UZVYWWWQVOUNSM',
	    'H\\LPMNOMXM RRMRXSZU[',
	    'H[MMMXNZP[S[UZVYWWWPVNUM',
	    'G]MMLNKPKVLXNZP[T[VZXXYVYPXNVMUMSNRPRb',
	    'IZWMLb RLMNNOPT_UaWb',
	    'G]RMRb RKMKVLXNZP[T[VZXXYVYM',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH RRMRXSZU[',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEQH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RTEQH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEQH',
	    'G\\L[LF RXFLR ROOX[Qb',
	    'H[SOUPVQWSWWVYUZS[P[NZMXMINGPFSFUGVIVLUNSOQO',
	    'H[JPKQLSLVMYNZP[S[UZVYWVWKVHUGSFPFNGMHLJLLMNNOPPWP',
	    'I\\KFMFOGQIRKR[ RRKSHTGVFWFYGZI',
	    'NiTEQH RXFZF\\G^I_K_[ R_K`HaGcFdFfGgI',
	    'I\\KFMFOGQIRKR[ RRKSHTGVFWFYGZI RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'G]RFRb RPMTMVNXPYRYVXXVZT[P[NZLXKVKRLPNNPM',
	    'F^RTRX R[MIM RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM',
	    'IZLMNNOPOXNZM[LZLXMVVRWPWNVMUNTPTXUZW[V^U`TaRb',
	    'G]R[Rb RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'H[R[Rb RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'FZWFQFNGLIKKJOJRKVLXNZQ[R[T\\U^U_TaSbQb',
	    'HZVMPMNNMOLQLWMYNZP[R[T\\U^U_TaRbPb',
	    'HZTPMP RM[MFWF',
	    'MZVPRP RWFUFSGRIR_QaOb',
	    'H\\MFOGPILSXNTXUZW[',
	    'I[RFMPWPR[',
	    'H\\NGNL RXIULTNTW RKIMGPFTFVGXIYKZOZUYYX[',
	    'H\\L[UR RR[WV RLMPNSPURWVXZXb',
	    'CaRWRR R\\XY]V`SaMa RLFJGHIGLGUHXJZL[N[PZQYRWSYTZV[X[ZZ\\X]U]L\\IZGXF',
	    'G]RTRX RXZW\\S`PaOa RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM',
	    'G]XFXb RPFNGLIKMKTLXNZP[T[VZXX',
	    'I\\WMWb RQMONNOMQMWNYOZQ[T[VZWY',
	    'F]KFK[ RKQMOPNTNVOXQYTYWXZW\\U^R`Nb',
	    'I[WLWMVPTRRSPSNRMPMONMPLRLTMVPWSWWVYUZS[M[',
	    'F]KHLGOFTFWGXHYJYLXOVQJ[N^Q_V_Y^',
	    'J[NNPMTMVNWPWRVTTVN[P]R^U^W]',
	    'G]I[[[ RIFJFLGXZZ[ R[FZFXGLZJ[',
	    'H[KMMNVZX[K[MZVNXM',
	    'G\\XEVFOFMGLHKJKWLYMZO[T[VZWYXWXPWNVMTLNLLMKN',
	    'H[WEVFTGPGNHMILKLWMYNZP[S[UZVYWWWQVOUNSMOMMNLO',
	    'G]RFRb RKQKMYMYQ',
	    'I[MMWM RRFRb',
	    'IZLMNNOPOXNZM[LZLXMVVRWPWNVMUNTPTXUZW[',
	    'H\\WbQbOaN`M^MQNOONQMTMVNWOXQXWWYVZT[Q[OZMX',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN',
	    'MWRMR_QaObNb RRFQGRHSGRFRH',
	    'G]KPYP RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'HZLTST RVZT[P[NZMYLWLQMONNPMTMVN',
	    'J\\XTQT RNZP[T[VZWYXWXQWOVNTMPMNN',
	    'G\\LFL[ RLKTKVLWMXOXRWTVUTVLV',
	    'H[MFMb RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH',
	    'F^K[KFRMYFY[',
	    'G]LbLMRSXMX[',
	    'G\\J`S` RMbMQNOONQMTMVNWOXQXWWYVZT[Q[OZMX',
	    'I^MYNZQ[S[VZXXYVZRZOYKXIVGSFQFNGMH',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RROQPRQSPRORQ',
	    'I^MYNZQ[S[VZXXYVZRZOYKXIVGSFQFNGMH RROQPRQSPRORQ',
	    'H[MPTP RW[M[MFWF RP>SA',
	    'H[MPTP RW[M[MFWF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'JbLFXF RR[RF RRMXM[N]P^S^\\]_[aXbVb',
	    'HZM[MFXF RT>QA',
	    'F[JPTP RWYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG',
	    'MWR[RF',
	    'MWR[RF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'JZUFUUTXRZO[M[',
	    'AbC[D[FZGXILJILGOFRFR[X[[Z]X^V^S]Q[OXNRN',
	    'AbF[FF RRFR[X[[Z]X^V^S]Q[OXNFN',
	    'JbLFXF RR[RF RRMXM[N]P^S^[',
	    'G\\L[LF RX[OO RXFLR RT>QA',
	    'G]LFL[XFX[ RP>SA',
	    'G[KFRT RYFPXNZL[K[ RN>O@QASAU@V>',
	    'G]R[R` RLFL[X[XF',
	    'I[MUWU RK[RFY[',
	    'G\\VFLFL[R[UZWXXVXSWQUORNLN',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP',
	    'HZM[MFXF',
	    'F^[`[[I[I` RW[WFRFPGOHNJL[',
	    'H[MPTP RW[M[MFWF',
	    'BbOOF[ RR[RF RRRFF R^[UO R^FRR',
	    'I]PPTP RMGOFTFVGWHXJXLWNVOTPWQXRYTYWXYWZU[O[MZ',
	    'G]LFL[XFX[',
	    'G]LFL[XFX[ RN>O@QASAU@V>',
	    'G\\L[LF RX[OO RXFLR',
	    'F\\W[WFTFQGOINLLXKZI[H[',
	    'F^K[KFRUYFY[',
	    'G]L[LF RLPXP RX[XF',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'G]L[LFXFX[',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH',
	    'JZLFXF RR[RF',
	    'G[KFRT RYFPXNZL[K[',
	    'G]R[RF RPITIWJYLZNZRYTWVTWPWMVKTJRJNKLMJPI',
	    'H\\KFY[ RYFK[',
	    'G]XFX[ RLFL[Z[Z`',
	    'H\\WFW[ RLFLNMPNQPRWR',
	    'CaRFR[ RHFH[\\[\\F',
	    'CaRFR[ RHFH[\\[\\F R\\[^[^`',
	    'F]HFMFM[S[VZXXYVYSXQVOSNMN',
	    'Da\\F\\[ RIFI[O[RZTXUVUSTQROONIN',
	    'H]MFM[S[VZXXYVYSXQVOSNMN',
	    'I^ZQPQ RMHNGQFSFVGXIYKZOZRYVXXVZS[Q[NZMY',
	    'CaHFH[ ROPHP RTFXFZG\\I]M]T\\XZZX[T[RZPXOTOMPIRGTF',
	    'G\\RQK[ RW[WFOFMGLHKJKMLOMPOQWQ',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR',
	    'H[WEVFTGPGNHMILKLWMYNZP[S[UZVYWWWQVOUNSMOMMNLO',
	    'I[STVUWWWXVZT[N[NMSMUNVPVQUSSTNT',
	    'JYO[OMWM',
	    'H[WOVNTMPMNNMOLQLWMYNZP[S[UZVYWWWJVHUGSFOFMG',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT',
	    'F^QTJ[ RRUJM RRMR[ RZ[ST RZMRU',
	    'K[RTTT RNNPMTMVNWPWQVSTTVUWWWXVZT[P[NZ',
	    'H\\MMM[WMW[',
	    'H\\MMM[WMW[ RNEOGQHSHUGVE',
	    'IZNMN[ RPSV[ RVMNU',
	    'I[V[VMSMQNPPOXNZL[',
	    'G]L[LMRXXMX[',
	    'H[MTVT RMMM[ RVMV[',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'H[M[MMVMV[',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN',
	    'KYMMWM RRMR[',
	    'JZMMR[ RWMR[P`OaMb',
	    'G]RFRb RPMTMVNXPYRYVXXVZT[P[NZLXKVKRLPNNPM',
	    'IZL[WM RLMW[',
	    'I\\WMW[ RNMN[Y[Y`',
	    'J\\VMV[ RNMNROTQUVU',
	    'F^RMR[ RKMK[Y[YM',
	    'F^RMR[ RKMK[Y[YM RY[[[[`',
	    'HZJMNMN[S[UZVXVUUSSRNR',
	    'F^YMY[ RKMK[P[RZSXSURSPRKR',
	    'IZNMN[S[UZVXVUUSSRNR',
	    'J\\XTQT RNNPMTMVNWOXQXWWYVZT[P[NZ',
	    'E_JTPT RJMJ[ RT[RZQYPWPQQORNTMWMYNZO[Q[WZYYZW[T[',
	    'I[RUM[ RV[VMPMNNMPMRNTPUVU',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RPESH',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'M^OKXK RRFR[ RRSSRUQWQYRZTZ[Y^WaVb',
	    'JYO[OMWM RTEQH',
	    'HZLTST RVZT[P[NZMYLWLQMONNPMTMVN',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN',
	    'MWR[RM RRFQGRHSGRFRH',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH RR[RM',
	    'MWRMR_QaObNb RRFQGRHSGRFRH',
	    'E^H[JZKXLPMNOMRMR[W[YZZXZUYSWRRR',
	    'D^IMI[ RRMR[W[YZZXZVYTWSIS',
	    'M^OKXK RRFR[ RRSSRUQWQYRZTZ[',
	    'IZNMN[ RPSV[ RVMNU RTEQH',
	    'H\\MMM[WMW[ RPESH',
	    'JZMMR[ RWMR[P`OaMb RNEOGQHSHUGVE',
	    'H]R[R` RMMM[W[WM',
	    'CaRWRR RLFJGHIGLGUHXJZL[N[PZQYRWSYTZV[X[ZZ\\X]U]L\\IZGXF',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM',
	    'F]IIVI RMFM[S[VZXXYVYSXQVOSNMN',
	    'HZJMTM RNFN[S[UZVXVUUSSRNR',
	    'D`IFI[ RYPIP R\\Y[ZX[V[SZQXPVOROOPKQISGVFXF[G\\H',
	    'F^KMK[ RWTKT RZZX[T[RZQYPWPQQORNTMXMZN',
	    'F^LSXS RRSR[ RH[RF\\[',
	    'I[NUVU RRUR[ RK[RMY[',
	    'AbF[FF RFS\\S RVSV[ RL[VF`[',
	    'E_J[JM RVUV[ RZUJU RO[VM][',
	    'E_R[RPJFZFRP RI[IVJSLQOPUPXQZS[V[[',
	    'G]R[RTLMXMRT RK[KXLVMUOTUTWUXVYXY[',
	    'AcF[FF RFPSP RV[VPNF^FVP RM[MVNSPQSPYP\\Q^S_V_[',
	    'DaI[IM RITST RV[VTPM\\MVT RO[OXPVQUSTYT[U\\V]X][',
	    'H\\OPSP RNAQFSBTAUA RLGNFSFUGVHWJWLVNUOSPVQWRXTXWWYVZT[O[M\\L^L_MaObWb',
	    'J[RTTT ROHRMTIUHVH RNNPMTMVNWPWQVSTTVUWWWXVZT[Q[O\\N^N_OaQbVb',
	    'G]R[RF RHFJGKIKNLQMROSUSWRXQYNYIZG\\F',
	    'G]RMRb RKMKVLXNZP[T[VZXXYVYM',
	    'G]KPYP RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'H[LTWT RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'I[KFR[YF',
	    'JZMMR[WM',
	    'I[KFR[YF ROAL> RVAS>',
	    'JZMMR[WM ROHLE RVHSE',
	    'GmPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF R`Me[ RjMe[c`ba`b',
	    'HkP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ R^Mc[ RhMc[a``a^b',
	    'CaRXR^ RRCRI RMFJGHIGLGUHXJZM[W[ZZ\\X]U]L\\IZGWFMF',
	    'G]RYR] RRKRO ROMMNLOKQKWLYMZO[U[WZXYYWYQXOWNUMOM',
	    'CaRWRR RLFJGHIGLGUHXJZL[N[PZQYRWSYTZV[X[ZZ\\X]U]L\\IZGXF RLBM@O?R?U@X@',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RLIMGOFRFUGXG',
	    'CaRWRR RLFJGHIGLGUHXJZL[N[PZQYRWSYTZV[X[ZZ\\X]U]L\\IZGXF RM<W< RR<R?',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RMEWE RRERH',
	    'FZWGTFPFMGKIJKJNKPMRPSTST[',
	    'FZVNTMPMNNMOLQLSMUNVPWTWT[',
	    'H[N]UO ROQWU RT[LW',
	    'JZMHMFWGWE',
	    'JZMHUEVH',
	    'NVPESH',
	    'NVTEQH',
	    'KZLIMGOFRFUGXG',
	    ':j>R?PAOCPDR RC^D\\F[H\\I^ RCFDDFCHDIF ROcPaR`TaUc ROAP?R>T?UA R[^\\\\^[`\\a^ R[F\\D^C`DaF R`RaPcOePfR',
	    ':jDQ>Q RH[D_ RHGDC RR_Re RRCR= R\\[`_ R\\G`C R`QfQ',
	    'G]LFL[XFX[ RX[[[Ub RN>O@QASAU@V>',
	    'H\\MMM[WMW[ RW[Z[Tb RNEOGQHSHUGVE',
	    'H]MFM[S[VZXXYVYSXQVOSNMN RJIPI',
	    'IZKMQM RNFN[S[UZVXVUUSSRNR',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ RTMXS',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ RSWW]',
	    'HZM[MFXFXA',
	    'JYO[OMWMWH',
	    'HZM[MFXF RJQRQ',
	    'JYO[OMWM RLTTT',
	    'H]M[MFXF RMMSMVNXPYSY\\X_VaSbQb',
	    'J\\O[OMWM ROTTTVUWVXXX[W^UaTb',
	    'BbOOF[ RR[RF RRRFF R^[UO R^FRR R^[`[``',
	    'F^QTJ[ RRUJM RRMR[ RZ[ST RZMRU RZ[\\[\\`',
	    'I]PPTP RMGOFTFVGWHXJXLWNVOTPWQXRYTYWXYWZU[O[MZ RR\\T]U_TaRbOb',
	    'K[RTTT RNNPMTMVNWPWQVSTTVUWWWXVZT[P[NZ RR\\T]U_TaRbOb',
	    'G\\L[LF RX[OO RXFLR RX[Z[Z`',
	    'IZNMN[ RPSV[ RVMNU RV[X[X`',
	    'G\\L[LF RX[OO RXFLR RPKPS',
	    'IZNMN[ RPSV[ RVMNU RRORW',
	    'G\\L[LF RX[OO RXFLR RIJOJ',
	    'IZN[NF RPSV[ RVMNU RKJQJ',
	    'E\\X[OO RXFLR RGFLFL[',
	    'HZPSV[ RVMNU RJMNMN[',
	    'G]L[LF RLPXP RX[XF RX[Z[Z`',
	    'H[MTVT RMMM[ RVMV[ RV[X[X`',
	    'GeL[LF RLPXP RX[XFcF',
	    'H`MTVT RMMM[ RV[VM^M',
	    'GhL[LFXFX[ RXM^MaNcPdSd\\c_aa^b\\b',
	    'HcM[MMVMV[ RVT[T]U^V_X_[^^\\a[b',
	    'F^QFNGLIKKJOJRKVLXNZQ[S[VZXXYVZRZMYJWIVITJSMSRTVUXWZY[[[',
	    'H\\QMPMNNMOLQLWMYNZP[T[VZWYXWXRWPUOSPRRRWSYTZV[Y[',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RR\\T]U_TaRbOb',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RR\\T]U_TaRbOb',
	    'JZLFXF RR[RF RR[T[T`',
	    'KYMMWM RRMR[ RR[T[T`',
	    'I[RQR[ RKFRQYF',
	    'JZR[Rb RMMR[WM',
	    'I[RQR[ RKFRQYF RNUVU',
	    'JZR[Rb RMMR[WM RN]V]',
	    'H\\KFY[ RYFK[ RX[Z[Z`',
	    'IZL[WM RLMW[ RV[X[X`',
	    'D]FFRF RXFX[ RLFL[Z[Z`',
	    'G\\RMIM RWMW[ RNMN[Y[Y`',
	    'H\\WFW[ RLFLNMPNQPRWR RW[Y[Y`',
	    'J\\VMV[ RNMNROTQUVU RV[X[X`',
	    'H\\WFW[ RLFLNMPNQPRWR RRNRV',
	    'J\\VMV[ RNMNROTQUVU RRQRY',
	    'G]L[LF RL[ RLPRPUQWSXVX[',
	    'H[M[MF RV[VPUNSMPMNNMO',
	    '@^WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGXIYKZOJQGQEPDOCMCK',
	    'E[VZT[P[NZMXMPNNPMTMVNWPWRMTKTISHQHO',
	    '@^WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGXIYKZOJQGQEPDOCMCK RR[P]O_PaRbTb',
	    'E[VZT[P[NZMXMPNNPMTMVNWPWRMTKTISHQHO RR[P]O_PaRbTb',
	    'MWR[RF',
	    'BbOOF[ RR[RF RRRFF R^[UO R^FRR RN>O@QASAU@V>',
	    'F^QTJ[ RRUJM RRMR[ RZ[ST RZMRU RNEOGQHSHUGVE',
	    'G\\L[LF RX[OO RXFLR RX[X_WaUbSb',
	    'IZNMN[ RPSV[ RVMNU RV[V_UaSbQb',
	    'F\\W[WFTFQGOINLLXKZI[H[ RW[Z[Tb',
	    'I[V[VMSMQNPPOXNZL[ RV[Y[Sb',
	    'G]L[LF RLPXP RX[XF RX[X_WaUbSb',
	    'H[MTVT RMMM[ RVMV[ RV[V_UaSbQb',
	    'G]L[LF RLPXP RX[XF RX[[[Ub',
	    'H[MTVT RMMM[ RVMV[ RV[Y[Sb',
	    'H\\WFW[ RLFLNMPNQPRWR RW[U[U`',
	    'J\\VMV[ RNMNROTQUVU RV[T[T`',
	    'F^K[KFRUYFY[ RY[\\[Vb',
	    'G]L[LMRXXMX[ RX[[[Ub',
	    'MWR[RF',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V>',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE',
	    'I[MUWU RK[RFY[ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'F`JURU RRPYP RH[OF\\F RRFR[\\[',
	    'D`INKMOMQNRP R[ZY[U[SZRXRPSNUMYM[N\\P\\RRSKSITHVHXIZK[O[QZRX',
	    'H[MPTP RW[M[MFWF RN>O@QASAU@V>',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNEOGQHSHUGVE',
	    'F^MHNGQFSFVGXIYKZOZRYVXXVZS[Q[NZLXKVJRZP',
	    'I[NNPMTMVNWPWXVZT[P[NZMXMVWT',
	    'F^MHNGQFSFVGXIYKZOZRYVXXVZS[Q[NZLXKVJRZP RNBOCNDMCNBND RVBWCVDUCVBVD',
	    'I[NNPMTMVNWPWXVZT[P[NZMXMVWT RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'BbOOF[ RR[RF RRRFF R^[UO R^FRR RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'F^QTJ[ RRUJM RRMR[ RZ[ST RZMRU RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I]PPTP RMGOFTFVGWHXJXLWNVOTPWQXRYTYWXYWZU[O[MZ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'K[RTTT RNNPMTMVNWPWQVSTTVUWWWXVZT[P[NZ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'H\\KFXFQNTNVOWPXRXWWYVZT[N[LZKY',
	    'JZMMVMOVRVTWUXVZV^U`TaRbObMa',
	    'G]LFL[XFX[ RM@W@',
	    'H\\MMM[WMW[ RMGWG',
	    'G]LFL[XFX[ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'H\\MMM[WMW[ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'G]KPYP RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF',
	    'H[LTWT RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'G]KPYP RPFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'H[LTWT RP[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I^ZPPP RMYNZQ[S[VZXXYVZRZOYKXIVGSFQFNGMH RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'J\\XTQT RNZP[T[VZWYXWXQWOVNTMPMNN RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'G[KFRT RYFPXNZL[K[ RM@W@',
	    'JZMMR[ RWMR[P`OaMb RMGWG',
	    'G[KFRT RYFPXNZL[K[ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'JZMMR[ RWMR[P`OaMb RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'G[KFRT RYFPXNZL[K[ RQ>NA RX>UA',
	    'JZMMR[ RWMR[P`OaMb RQENH RXEUH',
	    'H\\WFW[ RLFLNMPNQPRWR RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'J\\VMV[ RNMNROTQUVU RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'HZM[MFXF RM[O[O`',
	    'JYO[OMWM RO[Q[Q`',
	    'Da\\F\\[ RIFI[O[RZTXUVUSTQROONIN RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'F^YMY[ RKMK[P[RZSXSURSPRKR RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'HZWFMFM[Q[Q_PaNbLb RJQRQ',
	    'JYWMOMO[S[S_RaPbNb RLTTT',
	    'H\\KFY[ RYFK[ RX[X_WaUbSb',
	    'IZL[WM RLMW[ RV[V_UaSbQb',
	    'H\\KFY[ RYFK[ RNPVP',
	    'IZL[WM RLMW[ RNTVT',
	    'G\\WFW[Q[NZLXKVKSLQNOQNWN',
	    'J[VMV[Q[OZNXNUOSQRVR',
	    'B_RXSZU[X[ZZ[X[M RRFRXQZO[L[IZGXFVFSGQIOLNRN',
	    'E]RXSZU[V[XZYXYQ RRMRXQZO[M[KZJXJUKSMRRR',
	    'IePPTP RMGOFTFVGWHXJXLWNVOTPVQWRXTXXYZ[[^[`ZaXaM',
	    'KbRTTT RNNPMTMVNWPWQVSTTVUWWWXXZZ[[[]Z^X^Q',
	    'I\\PPTP RMGOFTFVGWHXJXLWNVOTPVQWRXTX[Z[Z`',
	    'K[RTTT RNNPMTMVNWPWQVSTTVUWWW[Y[Y`',
	    'FdH[I[KZLXNLOIQGTFWFWXXZZ[][_Z`X`M',
	    'IaL[NZOXPPQNSMVMVXWZY[Z[\\Z]X]Q',
	    'CaH[HF RHPTP RTFTXUZW[Z[\\Z]X]M',
	    'F^KTTT RKMK[ RTMTXUZW[X[ZZ[X[R',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR',
	    'HZUNSMPMNNMOLQLWMYNZP[T[VZVUSU',
	    'J_LFXF RRFRXSZU[X[ZZ[X[M',
	    'K]MMWM RRMRXSZU[V[XZYXYS',
	    'G[PPTP RWGUFPFNGMHLJLLMNNOPPMQLRKTKWLYMZO[U[WZ',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN',
	    'F\\W[WFTFQGOINLLXKZI[H[ RW[W_VaTbRb',
	    'I[V[VMSMQNPPOXNZL[ RV[V_UaSbQb',
	    'BaP[^F RD[E[GZHXJLKIMGPF^[',
	    'E^[MO[ RH[JZKXLPMNOM[[',
	    'E_\\FUO\\[ RJ[JFRFTGUHVJVMUOTPRQJQ',
	    'F^KMKb R[MUT[[ RKNMMQMSNTOUQUWTYSZQ[M[KZ',
	    'DaOQH[ RTFT[^[ R[QLQJPIOHMHJIHJGLF^F',
	    'D`H[MU RRPRMKMINHPHRITKURU R[ZY[U[SZRXRPSNUMYM[N\\P\\RRT',
	    'G]Z]X\\VZSWQVOV RP[NZLXKTKMLINGPFTFVGXIYMYTXXVZT[P[',
	    'I\\WMWb RWZU[Q[OZNYMWMQNOONQMUMWN',
	    'F^IFN[RLV[[F',
	    'G]JMN[RQV[ZM',
	    'G\\L[LF RX[OO RXFLR RXKRG',
	    'IZNMN[ RPSV[ RVMNU RWQQM',
	    'FgW[WFTFQGOINLLXKZI[H[ RWM]M`NbPcSc\\b_`a]b[b',
	    'IcV[VMSMQNPPOXNZL[ RVT[T]U^V_X_[^^\\a[b',
	    'GhL[LF RLPXP RX[XF RXM^MaNcPdSd\\c_aa^b\\b',
	    'HcMTVT RMMM[ RVMV[ RVT[T]U^V_X_[^^\\a[b',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'JZNXVX RM[RMW[',
	    'H\\LXRX RRTWT RRMR[Y[ RYMPMK[',
	    'D`[ZY[U[SZRX RINKMOMQNRPRXQZO[K[IZHXHVRUYU[T\\R\\P[NYMUMSNRP',
	    'I[STVUWWWXVZT[N[NMSMUNVPVQUSSTNT RKWQW',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN',
	    'J[SMOMO[S[UZVYWVWRVOUNSM',
	    'J[SMOMO[S[UZVYWVWRVOUNSM RLTRT',
	    'JYOTTT RVMOMO[V[',
	    'J[TTVSWQWPVNTMPMNN RRTTTVUWWWXVZT[P[NZ',
	    'MWRMR[ RRbSaR`QaRbR`',
	    'LYTMTWSYRZP[O[',
	    'IZNMN[ RPSV[ RVMNU',
	    'JYOMO[V[ RLVRR',
	    'G]L[LMRXXMX[',
	    'I\\W[WMN[NM',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[',
	    'J\\NNPMTMVNWOXQXWWYVZT[P[NZ',
	    'G]YSYVXXWYUZOZMYLXKVKSLQMPOOUOWPXQYS',
	    'G]XYYWYSXQWPUOOOMPLQKSKWLY',
	    'G]YNK[ RYSYVXXWYUZOZMYLXKVKSLQMPOOUOWPXQYS',
	    'DaINKMOMQNRPRXQZO[K[IZHXHVRT RRWSYTZV[Y[[Z\\Y]W]Q\\O[NYMVMTNSORQ',
	    'G]OMNNMPNRPS RTSVRWPVNUM RPSTSVTWVWXVZT[P[NZMXMVNTPS',
	    'I\\XTXQWOVNTMQMONNOMQMT',
	    'H[LTLWMYNZP[S[UZVYWWWT',
	    'I[N[NMTMVNWPWRVTTUNU',
	    'I[RUM[ RV[VMPMNNMPMRNTPUVU',
	    'I[RSMM RVMV[P[NZMXMVNTPSVS',
	    'KYMMWM RRMR[',
	    'H[MMMXNZP[S[UZVXVM',
	    'G]KPYP RKYVYXXYVYSXQWP',
	    '@]KPYP RKYVYXXYVYSXQWP REWFXEYDXEWEY REOFPEQDPEOEQ',
	    'G]KKYK RWKXLYNYQXSVTKT RVTXUYWYZX\\V]K]',
	    'JZMMR[WM',
	    'G]JMN[RQV[ZM',
	    'IZLMWML[W[',
	    'JZNMVMRRSRUSVUVXUZS[P[NZ',
	    'H\\XNUMPMNNMOLQLSMUNVPWTXVYWZX\\X^W`VaTbObLa RRTR\\',
	    'JZW[PROPPNRMTNUPTRM[',
	    'JYO[OMWM',
	    'JZM[RMW[',
	    'H[M[MMVMV[',
	    'I[N[NMTMVNWPWRVTTUNU',
	    'I[RMR[ RLMMNMRNTPUTUVTWRWNXM',
	    'I[V[VMSMQNPPOXNZL[',
	    'JZNKVK RMNR@WN',
	    'H\\LKRK RRGWG RR@RNYN RY@P@KN',
	    'I[SGVHWJWKVMTNNNN@S@UAVCVDUFSGNG',
	    'I[SGVHWJWKVMTNNNN@S@UAVCVDUFSGNG RKGQG',
	    'J[S@O@ONSNUMVLWIWEVBUAS@',
	    'JYOGTG RV@O@ONVN',
	    'KZUGPG RN@U@UNNN',
	    'HZUAS@P@NAMBLDLJMLNMPNTNVMVHSH',
	    'H[MGVG RM@MN RV@VN',
	    'MWRNR@ RUNON RU@O@',
	    'LYT@TJSLRMPNON',
	    'IZN@NN RPFVN RV@NH',
	    'JYO@ONVN',
	    'G]LNL@RKX@XN',
	    'H[MNM@VNV@',
	    'I\\WNW@NNN@',
	    'H[PNNMMLLJLDMBNAP@S@UAVBWDWJVLUMSNPN',
	    'G]O@NAMCNEPF RTFVEWCVAU@ RPFTFVGWIWKVMTNPNNMMKMINGPF',
	    'I[NNN@T@VAWCWEVGTHNH',
	    'I[RHWN RNNN@T@VAWCWEVGTHNH',
	    'KYM@W@ RR@RN',
	    'H[M@MKNMPNSNUMVKV@',
	    'G]J@NNRDVNZ@',
	    'KZOEQDSDUEVGVN RVMTNQNOMNKOIQHVH',
	    'JYNDNKOMQNSNUM RNEPDSDUEVGUISJNJ',
	    'H]WDUKTMRNPNNMMKMGNEPDRDTEVMWN',
	    'H\\XMVNUNSMRK RLDODQERHRKQMONNNLMKKKJVJXIYGXEVDUDSERH',
	    'KYO@ON ROMQNSNUMVKVGUESDQDOE',
	    'KYU@UN RUESDQDOENGNKOMQNSNUM',
	    'LYVMTNRNPMOKOGPERDSDUEVGVHOI',
	    'LYOEQDSDUEVGVKUMSNRNPMOKOJVI',
	    'LXPIRI RUETDPDOEOHPIOJOMPNTNUM',
	    'LXRITI ROEPDTDUEUHTIUJUMTNPNOM',
	    'KYUDUPTRRSOS RUESDQDOENGNKOMQNSNUM',
	    'NVRDRN RRUSTRSQTRURS',
	    'IZO@ON RUNQH RUDOJ',
	    'G]KNKD RKEMDODQERGRN RRGSEUDVDXEYGYN',
	    'KZODON ROEQDSDUEVGVPURSSRS',
	    'KYQNOMNKNGOEQDSDUEVGVKUMSNQN',
	    'LYOEQDSDUEVGVKUMSNQNOM',
	    'KYNINGOEQDSDUEVGVI',
	    'KYNINKOMQNSNUMVKVI',
	    'KYOSOD ROEQDSDUEVGVKUMSNQNOM',
	    'NXPDVD RR@RKSMUNVN',
	    'KYUDUN RNDNKOMQNSNUM',
	    'I[MFWF RMMTMVLWJWHVF',
	    'G]YDYN RYMWNUNSMRKRD RRKQMONNNLMKKKD',
	    'LXNDRNVD',
	    'LXVNPGPEQDSDTETGNN',
	    'KYSFRF RNSOQOCPAR@S@UAVCUESFUGVIVKUMSNQNOM',
	    'KXRMRS RMDOERMVD',
	    'KYSDQDOENGNKOMQNSNUMVKVGUESDPCOBOAP@U@',
	    'I[MDLFLJMLNMPNTNVMWLXJXGWEUDSERGRS',
	    'LXVDNS RNDPETRVS',
	    'NVRWRa RRPQQRRSQRPRR',
	    'LWPWPa RPZQXSWUW',
	    'KYUWUa RNWN^O`QaSaU`',
	    'LXNWRaVW',
	    'KYSYRY RNfOdOVPTRSSSUTVVUXSYUZV\\V^U`SaQaO`',
	    'KXR`Rf RMWOXR`VW',
	    'KYOfOZPXRWSWUXVZV^U`SaQaO`',
	    'I[MWLYL]M_N`PaTaV`W_X]XZWXUWSXRZRf',
	    'LXVWNf RNWPXTeVf',
	    'D`IMIXJZL[O[QZRX R[ZY[U[SZRXRPSNUMYM[N\\P\\RRT',
	    'H[M[MF RMNOMSMUNVOWQWWVYUZS[O[MZ RIHJGLFPHRGSF',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RQHRGTFXHZG[F',
	    'MYOMWM RR[RISGUFWF RMTNSPRTTVSWR',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[ RMTNSPRTTVSWR',
	    'I\\NMN[ RNOONQMTMVNWPW[ RMTNSPRTTVSWR',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ RI`J_L^P`R_S^',
	    'KXP[PM RPQQORNTMVM RLTMSORSTUSVR',
	    'KXM[S[ RVMTMRNQOPRP[ RLTMSORSTUSVR',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RNTOSQRUTWSXR',
	    'MYOMWM RRFRXSZU[W[ RMSNRPQTSVRWQ',
	    'IZLMWML[W[ RMTNSPRTTVSWR',
	    'H[M[MJNHOGQFTFVG RMNOMSMUNVOWQWWVYUZS[O[MZ',
	    'H[MGVG RM@MN RV@VN',
	    'JZMMVMOURUTVUWVYV^U`TaRbPbNaM_M^N\\P[V[',
	    'MlOMWM RRFRXSZU[W[ R^[^F Rg[gPfNdMaM_N^O RiC]`',
	    'MWR[RM RU[O[ RUMOM ROTUT',
	    'MXRMRXSZU[ ROTUT',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ RHT\\T',
	    'H[MMMXNZP[S[UZVXVM RHT\\T',
	    'I\\XMUMUPWRXTXWWYVZT[Q[OZNYMWMTNRPPPMMM RHU\\U',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'I[MUWU RK[RFY[ RR`TaUcTeRfPeOcPaR`',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RR`TaUcTeRfPeOcPaR`',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP RR?Q@RAS@R?RA',
	    'H[M[MF RMNOMSMUNVOWQWWVYUZS[O[MZ RN?M@NAO@N?NA',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP RRbSaR`QaRbR`',
	    'H[M[MF RMNOMSMUNVOWQWWVYUZS[O[MZ RRbSaR`QaRbR`',
	    'G\\SPVQWRXTXWWYVZT[L[LFSFUGVHWJWLVNUOSPLP RWaMa',
	    'H[M[MF RMNOMSMUNVOWQWWVYUZS[O[MZ RWaMa',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RR\\T]U_TaRbOb RT>QA',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RR\\T]U_TaRbOb RTEQH',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RR?Q@RAS@R?RA',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RV?U@VAW@V?VA',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RRbSaR`QaRbR`',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RSbTaS`RaSbS`',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RWaMa',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RXaNa',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RQ\\S]T_SaQbNb',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RS\\U]V_UaSbPb',
	    'G\\L[LFQFTGVIWKXOXRWVVXTZQ[L[ RVcR`Nc',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RWcS`Oc',
	    'H[MPTP RW[M[MFWF RM@W@ RP9S<',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RMGWG RP>SA',
	    'H[MPTP RW[M[MFWF RM@W@ RT9Q<',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RMGWG RT>QA',
	    'H[MPTP RW[M[MFWF RVcR`Nc',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RVcR`Nc',
	    'H[MPTP RW[M[MFWF RW`VaTbP`NaMb',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RW`VaTbP`NaMb',
	    'H[MPTP RW[M[MFWF RR\\T]U_TaRbOb RN>O@QASAU@V>',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RR\\T]U_TaRbOb RNEOGQHSHUGVE',
	    'HZTPMP RM[MFWF RR?Q@RAS@R?RA',
	    'MYOMWM RR[RISGUFWF RT?S@TAU@T?TA',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RM@W@',
	    'I\\WMW^V`UaSbPbNa RWZU[Q[OZNYMWMQNOONQMUMWN RMGWG',
	    'G]L[LF RLPXP RX[XF RR?Q@RAS@R?RA',
	    'H[M[MF RV[VPUNSMPMNNMO RM?L@MAN@M?MA',
	    'G]L[LF RLPXP RX[XF RRbSaR`QaRbR`',
	    'H[M[MF RV[VPUNSMPMNNMO RRbSaR`QaRbR`',
	    'G]L[LF RLPXP RX[XF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'H[M[MF RV[VPUNSMPMNNMO RI?J@IAH@I?IA RQ?R@QAP@Q?QA',
	    'G]L[LF RLPXP RX[XF RL\\N]O_NaLbIb',
	    'H[M[MF RV[VPUNSMPMNNMO RM\\O]P_OaMbJb',
	    'G]L[LF RLPXP RX[XF RV`UbScQcObN`',
	    'H[M[MF RV[VPUNSMPMNNMO RV`UbScQcObN`',
	    'MWR[RF RW`VaTbP`NaMb',
	    'MWR[RM RRFQGRHSGRFRH RW`VaTbP`NaMb',
	    'MWR[RF RN?O@NAM@N?NA RV?W@VAU@V?VA RT9Q<',
	    'MWR[RM RNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'G\\L[LF RX[OO RXFLR RT>QA',
	    'IZN[NF RPSV[ RVMNU RPAMD',
	    'G\\L[LF RX[OO RXFLR RRbSaR`QaRbR`',
	    'IZN[NF RPSV[ RVMNU RRbSaR`QaRbR`',
	    'G\\L[LF RX[OO RXFLR RWaMa',
	    'IZN[NF RPSV[ RVMNU RWaMa',
	    'HYW[M[MF RRbSaR`QaRbR`',
	    'MXU[SZRXRF RSbTaS`RaSbS`',
	    'HYW[M[MF RH@R@ RRbSaR`QaRbR`',
	    'MXU[SZRXRF RM@W@ RSbTaS`RaSbS`',
	    'HYW[M[MF RWaMa',
	    'MXU[SZRXRF RXaNa',
	    'HYW[M[MF RVcR`Nc',
	    'MXU[SZRXRF RWcS`Oc',
	    'F^K[KFRUYFY[ RT>QA',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[ RTEQH',
	    'F^K[KFRUYFY[ RR?Q@RAS@R?RA',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[ RRFQGRHSGRFRH',
	    'F^K[KFRUYFY[ RRbSaR`QaRbR`',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[ RRbSaR`QaRbR`',
	    'G]L[LFX[XF RR?Q@RAS@R?RA',
	    'I\\NMN[ RNOONQMTMVNWPW[ RRFQGRHSGRFRH',
	    'G]L[LFX[XF RRbSaR`QaRbR`',
	    'I\\NMN[ RNOONQMTMVNWPW[ RRbSaR`QaRbR`',
	    'G]L[LFX[XF RWaMa',
	    'I\\NMN[ RNOONQMTMVNWPW[ RWaMa',
	    'G]L[LFX[XF RVcR`Nc',
	    'I\\NMN[ RNOONQMTMVNWPW[ RVcR`Nc',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RMAN@P?TAV@W? RT9Q<',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMHNGPFTHVGWF RT>QA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RMAN@P?TAV@W? RN:O;N<M;N:N< RV:W;V<U;V:V<',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMHNGPFTHVGWF RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RM@W@ RP9S<',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMGWG RP>SA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RM@W@ RT9Q<',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RMGWG RT>QA',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ RT>QA',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ RTEQH',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ RR?Q@RAS@R?RA',
	    'H[MMMb RMNOMSMUNVOWQWWVYUZS[O[MZ RRFQGRHSGRFRH',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RR?Q@RAS@R?RA',
	    'KXP[PM RPQQORNTMVM RSFRGSHTGSFSH',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RRbSaR`QaRbR`',
	    'KXP[PM RPQQORNTMVM RPbQaP`OaPbP`',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RM@W@ RRbSaR`QaRbR`',
	    'KXP[PM RPQQORNTMVM RNGXG RPbQaP`OaPbP`',
	    'G\\X[QQ RL[LFTFVGWHXJXMWOVPTQLQ RWaMa',
	    'KXP[PM RPQQORNTMVM RUaKa',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RR?Q@RAS@R?RA',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RRFQGRHSGRFRH',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RRbSaR`QaRbR`',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RRbSaR`QaRbR`',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RU>RA RM>N?M@L?M>M@',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RUERH RMENFMGLFMEMG',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RN>RAV> RR:Q;R<S;R:R<',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RNERHVE RR?Q@RAS@R?RA',
	    'H\\LZO[T[VZWYXWXUWSVRTQPPNOMNLLLJMHNGPFUFXG RR?Q@RAS@R?RA RRbSaR`QaRbR`',
	    'J[NZP[T[VZWXWWVUTTQTOSNQNPONQMTMVN RRFQGRHSGRFRH RRbSaR`QaRbR`',
	    'JZLFXF RR[RF RR?Q@RAS@R?RA',
	    'MYOMWM RRFRXSZU[W[ RR?Q@RAS@R?RA',
	    'JZLFXF RR[RF RRbSaR`QaRbR`',
	    'MYOMWM RRFRXSZU[W[ RTbUaT`SaTbT`',
	    'JZLFXF RR[RF RWaMa',
	    'MYOMWM RRFRXSZU[W[ RYaOa',
	    'JZLFXF RR[RF RVcR`Nc',
	    'MYOMWM RRFRXSZU[W[ RXcT`Pc',
	    'G]LFLWMYNZP[T[VZWYXWXF RVbUaV`WaVbV` RNbMaN`OaNbN`',
	    'H[VMV[ RMMMXNZP[S[UZVY RVbUaV`WaVbV` RNbMaN`OaNbN`',
	    'G]LFLWMYNZP[T[VZWYXWXF RW`VaTbP`NaMb',
	    'H[VMV[ RMMMXNZP[S[UZVY RW`VaTbP`NaMb',
	    'G]LFLWMYNZP[T[VZWYXWXF RVcR`Nc',
	    'H[VMV[ RMMMXNZP[S[UZVY RVcR`Nc',
	    'G]LFLWMYNZP[T[VZWYXWXF RMAN@P?TAV@W? RT9Q<',
	    'H[VMV[ RMMMXNZP[S[UZVY RMHNGPFTHVGWF RT>QA',
	    'G]LFLWMYNZP[T[VZWYXWXF RM@W@ RN:O;N<M;N:N< RV:W;V<U;V:V<',
	    'H[VMV[ RMMMXNZP[S[UZVY RMGWG RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'I[KFR[YF RMAN@P?TAV@W?',
	    'JZMMR[WM RMHNGPFTHVGWF',
	    'I[KFR[YF RRbSaR`QaRbR`',
	    'JZMMR[WM RRbSaR`QaRbR`',
	    'F^IFN[RLV[[F RP>SA',
	    'G]JMN[RQV[ZM RPESH',
	    'F^IFN[RLV[[F RT>QA',
	    'G]JMN[RQV[ZM RTEQH',
	    'F^IFN[RLV[[F RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'G]JMN[RQV[ZM RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'F^IFN[RLV[[F RR?Q@RAS@R?RA',
	    'G]JMN[RQV[ZM RRFQGRHSGRFRH',
	    'F^IFN[RLV[[F RRbSaR`QaRbR`',
	    'G]JMN[RQV[ZM RRbSaR`QaRbR`',
	    'H\\KFY[ RYFK[ RR?Q@RAS@R?RA',
	    'IZL[WM RLMW[ RRFQGRHSGRFRH',
	    'H\\KFY[ RYFK[ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'IZL[WM RLMW[ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I[RQR[ RKFRQYF RR?Q@RAS@R?RA',
	    'JZMMR[ RWMR[P`OaMb RRFQGRHSGRFRH',
	    'H\\KFYFK[Y[ RNAR>VA',
	    'IZLMWML[W[ RNHREVH',
	    'H\\KFYFK[Y[ RRbSaR`QaRbR`',
	    'IZLMWML[W[ RRbSaR`QaRbR`',
	    'H\\KFYFK[Y[ RWaMa',
	    'IZLMWML[W[ RWaMa',
	    'H[M[MF RV[VPUNSMPMNNMO RWaMa',
	    'MYOMWM RRFRXSZU[W[ RN?O@NAM@N?NA RV?W@VAU@V?VA',
	    'G]JMN[RQV[ZM RRHPGOEPCRBTCUETGRH',
	    'JZMMR[ RWMR[P`OaMb RRHPGOEPCRBTCUETGRH',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RWJYIZGYEWD',
	    'MYR[RISGUFWF RT?S@TAU@T?TA',
	    'MYR[RISGUFWF ROSUO',
	    'MYR[RISGUFWF ROLUL',
	    'E^J[JLKIMGPFZFSNVNXOYPZRZWYYXZV[R[PZOY',
	    'H[SMPMNNMOLQLWMYNZP[S[UZVYWWWQVOUNSMPLNKMINGPFTFVG',
	    'I[MUWU RK[RFY[ RRbSaR`QaRbR`',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RRbSaR`QaRbR`',
	    'I[MUWU RK[RFY[ RRAT?U=T;R:P:',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RRHTFUDTBRAPA',
	    'I[MUWU RK[RFY[ RU>X; RNAR>VA',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RUEXB RNHREVH',
	    'I[MUWU RK[RFY[ RO>L; RNAR>VA',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR ROELB RNHREVH',
	    'I[MUWU RK[RFY[ RNAR>VA RXAZ?[=Z;X:V:',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNHREVH RXHZF[DZBXAVA',
	    'I[MUWU RK[RFY[ RNAR>VA RM<N;P:T<V;W:',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNHREVH RMAN@P?TAV@W?',
	    'I[MUWU RK[RFY[ RNAR>VA RRbSaR`QaRbR`',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNHREVH RRbSaR`QaRbR`',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V> RT9Q<',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE RT>QA',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V> RP9S<',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE RP>SA',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V> RP>R<S:R8P7N7',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE RPERCSAR?P>N>',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V> RM<N;P:T<V;W:',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE RMAN@P?TAV@W?',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V> RRbSaR`QaRbR`',
	    'I\\W[WPVNTMPMNN RWZU[P[NZMXMVNTPSUSWR RNEOGQHSHUGVE RRbSaR`QaRbR`',
	    'H[MPTP RW[M[MFWF RRbSaR`QaRbR`',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RRbSaR`QaRbR`',
	    'H[MPTP RW[M[MFWF RRAT?U=T;R:P:',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RRHTFUDTBRAPA',
	    'H[MPTP RW[M[MFWF RMAN@P?TAV@W?',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RMHNGPFTHVGWF',
	    'H[MPTP RW[M[MFWF RU>X; RNAR>VA',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RUEXB RNHREVH',
	    'H[MPTP RW[M[MFWF RO>L; RNAR>VA',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT ROELB RNHREVH',
	    'H[MPTP RW[M[MFWF RNAR>VA RXAZ?[=Z;X:V:',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNHREVH RXHZF[DZBXAVA',
	    'H[MPTP RW[M[MFWF RNAR>VA RM<N;P:T<V;W:',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNHREVH RMAN@P?TAV@W?',
	    'H[MPTP RW[M[MFWF RNAR>VA RRbSaR`QaRbR`',
	    'I[VZT[P[NZMXMPNNPMTMVNWPWRMT RNHREVH RRbSaR`QaRbR`',
	    'MWR[RF RRAT?U=T;R:P:',
	    'MWR[RM RRHTFUDTBRAPA',
	    'MWR[RF RRbSaR`QaRbR`',
	    'MWR[RM RRFQGRHSGRFRH RRbSaR`QaRbR`',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RRbSaR`QaRbR`',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RRbSaR`QaRbR`',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RRAT?U=T;R:P:',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RRHTFUDTBRAPA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RU>X; RNAR>VA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUEXB RNHREVH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RO>L; RNAR>VA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ ROELB RNHREVH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RNAR>VA RXAZ?[=Z;X:V:',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNHREVH RXHZF[DZBXAVA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RNAR>VA RM<N;P:T<V;W:',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNHREVH RMAN@P?TAV@W?',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RNAR>VA RRbSaR`QaRbR`',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RNHREVH RRbSaR`QaRbR`',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RVGXFYDXBWA RT>QA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUNWMXKWIVH RTEQH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RVGXFYDXBWA RP>SA',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUNWMXKWIVH RPESH',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RVGXFYDXBWA RRAT?U=T;R:P:',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUNWMXKWIVH RRHTFUDTBRAPA',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RVGXFYDXBWA RWAVBTCPANBMC',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUNWMXKWIVH RWHVITJPHNIMJ',
	    'G]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RVGXFYDXBWA RRbSaR`QaRbR`',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RUNWMXKWIVH RRbSaR`QaRbR`',
	    'G]LFLWMYNZP[T[VZWYXWXF RRbSaR`QaRbR`',
	    'H[VMV[ RMMMXNZP[S[UZVY RRbSaR`QaRbR`',
	    'G]LFLWMYNZP[T[VZWYXWXF RRAT?U=T;R:P:',
	    'H[VMV[ RMMMXNZP[S[UZVY RRHTFUDTBRAPA',
	    'G]LFLWMYNZP[T[VZWYXWXF RXFZE[CZAY@ RT>QA',
	    'H[VMV[ RMMMXNZP[S[UZVY RVMXLYJXHWG RTEQH',
	    'G]LFLWMYNZP[T[VZWYXWXF RXFZE[CZAY@ RP>SA',
	    'H[VMV[ RMMMXNZP[S[UZVY RVMXLYJXHWG RPESH',
	    'G]LFLWMYNZP[T[VZWYXWXF RXFZE[CZAY@ RRAT?U=T;R:P:',
	    'H[VMV[ RMMMXNZP[S[UZVY RVMXLYJXHWG RRHTFUDTBRAPA',
	    'G]LFLWMYNZP[T[VZWYXWXF RXFZE[CZAY@ RWAVBTCPANBMC',
	    'H[VMV[ RMMMXNZP[S[UZVY RVMXLYJXHWG RWHVITJPHNIMJ',
	    'G]LFLWMYNZP[T[VZWYXWXF RXFZE[CZAY@ RRbSaR`QaRbR`',
	    'H[VMV[ RMMMXNZP[S[UZVY RVMXLYJXHWG RRbSaR`QaRbR`',
	    'I[RQR[ RKFRQYF RP>SA',
	    'JZMMR[ RWMR[P`OaMb RPESH',
	    'I[RQR[ RKFRQYF RRbSaR`QaRbR`',
	    'JZMMR[ RWMR[P`OaMb RVbWaV`UaVbV`',
	    'I[RQR[ RKFRQYF RRAT?U=T;R:P:',
	    'JZMMR[ RWMR[P`OaMb RRHTFUDTBRAPA',
	    'I[RQR[ RKFRQYF RMAN@P?TAV@W?',
	    'JZMMR[ RWMR[P`OaMb RMHNGPFTHVGWF',
	    'E\\PFP[ RJFJ[Z[',
	    'J[MMWM ROFOXPZR[ RX[VZUXUF',
	    'G]QFOGMJLMLWMYNZP[T[VZXXYVYTXPVMUL',
	    'H[QMONNOMQMWNYOZQ[S[UZVYWWWUVSURSQ',
	    'G[KFRT RYFRTPXOZM[KZJXKVMUOVPX',
	    'JZMMR[ RWMR[Q_PaNbLaK_L]N\\P]Q_',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQHRHSGSE',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQEQGRHSH',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEWH RMHNHOGOE',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEWH RMEMGNHOH',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RXEUH RMHNHOGOE',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RXEUH RMEMGNHOH',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQHRHSGSE RMAN@P?TAV@W?',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQEQGRHSH RMAN@P?TAV@W?',
	    'G[MUWU RK[RFY[ RJHKHLGLE',
	    'G[MUWU RK[RFY[ RJEJGKHLH',
	    '?[MUWU RK[RFY[ RIELH RBHCHDGDE',
	    '?[MUWU RK[RFY[ RIELH RBEBGCHDH',
	    '?[MUWU RK[RFY[ RMEJH RBHCHDGDE',
	    '?[MUWU RK[RFY[ RMEJH RBEBGCHDH',
	    'D[MUWU RK[RFY[ RFAG@I?MAO@P? RJHKHLGLE',
	    'D[MUWU RK[RFY[ RFAG@I?MAO@P? RJEJGKHLH',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RQHRHSGSE',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RQEQGRHSH',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RTEWH RMHNHOGOE',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RTEWH RMEMGNHOH',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RXEUH RMHNHOGOE',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RXEUH RMEMGNHOH',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'B[MPTP RW[M[MFWF REHFHGGGE',
	    'B[MPTP RW[M[MFWF REEEGFHGH',
	    ':[MPTP RW[M[MFWF RDEGH R=H>H?G?E',
	    ':[MPTP RW[M[MFWF RDEGH R=E=G>H?H',
	    ':[MPTP RW[M[MFWF RHEEH R=H>H?G?E',
	    ':[MPTP RW[M[MFWF RHEEH R=E=G>H?H',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'I\\NMN[ RNOONQMTMVNWPWb RQHRHSGSE',
	    'I\\NMN[ RNOONQMTMVNWPWb RQEQGRHSH',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEWH RMHNHOGOE',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEWH RMEMGNHOH',
	    'I\\NMN[ RNOONQMTMVNWPWb RXEUH RMHNHOGOE',
	    'I\\NMN[ RNOONQMTMVNWPWb RXEUH RMEMGNHOH',
	    'I\\NMN[ RNOONQMTMVNWPWb RQHRHSGSE RMAN@P?TAV@W?',
	    'I\\NMN[ RNOONQMTMVNWPWb RQEQGRHSH RMAN@P?TAV@W?',
	    'A]L[LF RLPXP RX[XF RDHEHFGFE',
	    'A]L[LF RLPXP RX[XF RDEDGEHFH',
	    '9]L[LF RLPXP RX[XF RCEFH R<H=H>G>E',
	    '9]L[LF RLPXP RX[XF RCEFH R<E<G=H>H',
	    '9]L[LF RLPXP RX[XF RGEDH R<H=H>G>E',
	    '9]L[LF RLPXP RX[XF RGEDH R<E<G=H>H',
	    '>]L[LF RLPXP RX[XF R@AA@C?GAI@J? RDHEHFGFE',
	    '>]L[LF RLPXP RX[XF R@AA@C?GAI@J? RDEDGEHFH',
	    'MXRMRXSZU[ RQHRHSGSE',
	    'MXRMRXSZU[ RQEQGRHSH',
	    'MXRMRXSZU[ RTEWH RMHNHOGOE',
	    'MXRMRXSZU[ RTEWH RMEMGNHOH',
	    'MXRMRXSZU[ RXEUH RMHNHOGOE',
	    'MXRMRXSZU[ RXEUH RMEMGNHOH',
	    'MXRMRXSZU[ RQHRHSGSE RMAN@P?TAV@W?',
	    'MXRMRXSZU[ RQEQGRHSH RMAN@P?TAV@W?',
	    'GWR[RF RJHKHLGLE',
	    'GWR[RF RJEJGKHLH',
	    '?WR[RF RIELH RBHCHDGDE',
	    '?WR[RF RIELH RBEBGCHDH',
	    '?WR[RF RMEJH RBHCHDGDE',
	    '?WR[RF RMEJH RBEBGCHDH',
	    'DWR[RF RFAG@I?MAO@P? RJHKHLGLE',
	    'DWR[RF RFAG@I?MAO@P? RJEJGKHLH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RQHRHSGSE',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RQEQGRHSH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEWH RMHNHOGOE',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEWH RMEMGNHOH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RXEUH RMHNHOGOE',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RXEUH RMEMGNHOH',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'B]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF REHFHGGGE',
	    'B]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF REEEGFHGH',
	    ':]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RDEGH R=H>H?G?E',
	    ':]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RDEGH R=E=G>H?H',
	    ':]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RHEEH R=H>H?G?E',
	    ':]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RHEEH R=E=G>H?H',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RQHRHSGSE',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RQEQGRHSH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RTEWH RMHNHOGOE',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RTEWH RMEMGNHOH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RXEUH RMHNHOGOE',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RXEUH RMEMGNHOH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RQHRHSGSE RMAN@P?TAV@W?',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RQEQGRHSH RMAN@P?TAV@W?',
	    'F^K[KFYFY[K[',
	    '@[RQR[ RKFRQYF RCECGDHEH',
	    'F^K[KFYFY[K[',
	    '8[RQR[ RKFRQYF RBEEH R;E;G<H=H',
	    'F^K[KFYFY[K[',
	    '8[RQR[ RKFRQYF RFECH R;E;G<H=H',
	    'F^K[KFYFY[K[',
	    '=[RQR[ RKFRQYF R?A@@B?FAH@I? RCECGDHEH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQHRHSGSE',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQEQGRHSH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEWH RMHNHOGOE',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEWH RMEMGNHOH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RXEUH RMHNHOGOE',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RXEUH RMEMGNHOH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQHRHSGSE RMAN@P?TAV@W?',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQEQGRHSH RMAN@P?TAV@W?',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RCHDHEGEE',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RCECGDHEH',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RBEEH R;H<H=G=E',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RBEEH R;E;G<H=H',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RFECH R;H<H=G=E',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RFECH R;E;G<H=H',
	    '=^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ R?A@@B?FAH@I? RCHDHEGEE',
	    '=^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ R?A@@B?FAH@I? RCECGDHEH',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEQH',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEQH',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RTEQH',
	    'IZPTNUMWMXNZP[T[VZ RRTPTNSMQMPNNPMTMVN RTEQH',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEQH',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEQH',
	    'MXRMRXSZU[ RTEQH',
	    'MXRMRXSZU[ RTEQH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEQH',
	    'H[P[NZMYLWLQMONNPMSMUNVOWQWWVYUZS[P[ RTEQH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RTEQH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RTEQH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEQH',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEQH',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQHRHSGSE RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQEQGRHSH RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEWH RMHNHOGOE RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEWH RMEMGNHOH RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RXEUH RMHNHOGOE RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RXEUH RMEMGNHOH RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQHRHSGSE RMAN@P?TAV@W? RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RQEQGRHSH RMAN@P?TAV@W? RR`RcSdTd',
	    'G[MUWU RK[RFY[ RJHKHLGLE RR`RcSdTd',
	    'G[MUWU RK[RFY[ RJEJGKHLH RR`RcSdTd',
	    '?[MUWU RK[RFY[ RIELH RBHCHDGDE RR`RcSdTd',
	    '?[MUWU RK[RFY[ RIELH RBEBGCHDH RR`RcSdTd',
	    '?[MUWU RK[RFY[ RMEJH RBHCHDGDE RR`RcSdTd',
	    '?[MUWU RK[RFY[ RMEJH RBEBGCHDH RR`RcSdTd',
	    'D[MUWU RK[RFY[ RFAG@I?MAO@P? RJHKHLGLE RR`RcSdTd',
	    'D[MUWU RK[RFY[ RFAG@I?MAO@P? RJEJGKHLH RR`RcSdTd',
	    'I\\NMN[ RNOONQMTMVNWPWb RQHRHSGSE RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RQEQGRHSH RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEWH RMHNHOGOE RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEWH RMEMGNHOH RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RXEUH RMHNHOGOE RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RXEUH RMEMGNHOH RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RQHRHSGSE RMAN@P?TAV@W? RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RQEQGRHSH RMAN@P?TAV@W? RN`NcOdPd',
	    'N]L[LF RLPXP RX[XF RR`RcSdTd',
	    'A]L[LF RLPXP RX[XF RDEDGEHFH RR`RcSdTd',
	    '9]L[LF RLPXP RX[XF RCEFH R<H=H>G>E RR`RcSdTd',
	    '9]L[LF RLPXP RX[XF RCEFH R<E<G=H>H RR`RcSdTd',
	    '9]L[LF RLPXP RX[XF RGEDH R<H=H>G>E RR`RcSdTd',
	    '9]L[LF RLPXP RX[XF RGEDH R<E<G=H>H RR`RcSdTd',
	    '>]L[LF RLPXP RX[XF R@AA@C?GAI@J? RDHEHFGFE RR`RcSdTd',
	    '>]L[LF RLPXP RX[XF R@AA@C?GAI@J? RDEDGEHFH RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQHRHSGSE RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQEQGRHSH RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEWH RMHNHOGOE RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEWH RMEMGNHOH RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RXEUH RMHNHOGOE RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RXEUH RMEMGNHOH RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQHRHSGSE RMAN@P?TAV@W? RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RQEQGRHSH RMAN@P?TAV@W? RR`RcSdTd',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RCHDHEGEE RR`RcSdTd',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RCECGDHEH RR`RcSdTd',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RBEEH R;H<H=G=E RR`RcSdTd',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RBEEH R;E;G<H=H RR`RcSdTd',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RFECH R;H<H=G=E RR`RcSdTd',
	    '8^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RFECH R;E;G<H=H RR`RcSdTd',
	    '=^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ R?A@@B?FAH@I? RCHDHEGEE RR`RcSdTd',
	    '=^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ R?A@@B?FAH@I? RCECGDHEH RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RNEOGQHSHUGVE',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RMGWG',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RPESH RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RR`RcSdTd',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RTEQH RR`RcSdTd',
	    'F^K[KFYFY[K[',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RMHNGPFTHVGWF',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RMHNGPFTHVGWF RR`RcSdTd',
	    'I[MUWU RK[RFY[ RN>O@QASAU@V>',
	    'I[MUWU RK[RFY[ RM@W@',
	    'G[MUWU RK[RFY[ RIELH',
	    'G[MUWU RK[RFY[ RMEJH',
	    'I[MUWU RK[RFY[ RR`RcSdTd',
	    'NVQHRHSGSE',
	    'NVR`RcSdTd',
	    'NVQHRHSGSE',
	    'KZMHNGPFTHVGWF',
	    'LXMCNBPATCVBWA RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I\\NMN[ RNOONQMTMVNWPWb RPESH RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RN`NcOdPd',
	    'I\\NMN[ RNOONQMTMVNWPWb RTEQH RN`NcOdPd',
	    'F^K[KFYFY[K[',
	    'I\\NMN[ RNOONQMTMVNWPWb RMHNGPFTHVGWF',
	    'I\\NMN[ RNOONQMTMVNWPWb RMHNGPFTHVGWF RN`NcOdPd',
	    'B[MPTP RW[M[MFWF RDEGH',
	    'B[MPTP RW[M[MFWF RHEEH',
	    'A]L[LF RLPXP RX[XF RCEFH',
	    'A]L[LF RLPXP RX[XF RGEDH',
	    'G]L[LF RLPXP RX[XF RR`RcSdTd',
	    'JZTEWH RMHNHOGOE',
	    'JZXEUH RMHNHOGOE',
	    'NVQHRHSGSE RMAN@P?TAV@W?',
	    'MXRMRXSZU[ RNEOGQHSHUGVE',
	    'MXRMRXSZU[ RMGWG',
	    'MXRMRXSZU[ RNFOGNHMGNFNH RVFWGVHUGVFVH RP>SA',
	    'MXRMRXSZU[ RNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'MXRMRXSZU[ RMHNGPFTHVGWF',
	    'MXRMRXSZU[ RMCNBPATCVBWA RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'MWR[RF RN>O@QASAU@V>',
	    'MWR[RF RM@W@',
	    'GWR[RF RIELH',
	    'GWR[RF RMEJH',
	    'F^K[KFYFY[K[',
	    'JZTEWH RMEMGNHOH',
	    'JZXEUH RMEMGNHOH',
	    'NVQEQGRHSH RMAN@P?TAV@W?',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RNEOGQHSHUGVE',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RMGWG',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RNFOGNHMGNFNH RVFWGVHUGVFVH RP>SA',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'H\\MbMQNOONQMTMVNWOXQXWWYVZT[Q[OZMX RQHRHSGSE',
	    'H\\MbMQNOONQMTMVNWOXQXWWYVZT[Q[OZMX RQEQGRHSH',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RMHNGPFTHVGWF',
	    'H[MMMXNZP[S[UZVYWWWPVNUM RMCNBPATCVBWA RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'I[RQR[ RKFRQYF RN>O@QASAU@V>',
	    'I[RQR[ RKFRQYF RM@W@',
	    '@[RQR[ RKFRQYF RBEEH',
	    '@[RQR[ RKFRQYF RFECH',
	    'A\\L[LFTFVGWHXJXMWOVPTQLQ RDEDGEHFH',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH RP>SA',
	    'LXNFOGNHMGNFNH RVFWGVHUGVFVH RT>QA',
	    'NVPESH',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RPESH RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RR`RcSdTd',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RTEQH RR`RcSdTd',
	    'F^K[KFYFY[K[',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RMHNGPFTHVGWF',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RMHNGPFTHVGWF RR`RcSdTd',
	    'B]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RDEGH',
	    'B]PFTFVGXIYMYTXXVZT[P[NZLXKTKMLINGPF RHEEH',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RBEEH',
	    '@^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RFECH',
	    'F^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[ RR`RcSdTd',
	    'NVTEQH',
	    'NVQEQGRHSH',
	    'F^K[KFYFY[K[',
	    'F^',
	    'LX',
	    'F^',
	    'LX',
	    'NV',
	    'OU',
	    'PT',
	    'H\\',
	    'MW',
	    'PT',
	    'QS',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'LXOTUT',
	    'LXOTUT',
	    'H\\JRZR',
	    'LXVTNT',
	    'F^IT[T',
	    'F^IT[T',
	    'H\\ODOb RUDUb',
	    'JZJbZb RJ]Z]',
	    'MWQGQFRDSC',
	    'MWSFSGRIQJ',
	    'MWSZS[R]Q^',
	    'MWQFQGRISJ',
	    'JZUGUFVDWC RMGMFNDOC',
	    'JZOFOGNIMJ RWFWGVIUJ',
	    'JZOZO[N]M^ RWZW[V]U^',
	    'JZUFUGVIWJ RMFMGNIOJ',
	    'I[MMWM RRFRb',
	    'I[M[W[ RMMWM RRFRb',
	    'E_PQPU RQUQQ RRPRV RSUSQ RTQTU RPTRVTT RPRRPTR RPQRPTQUSTURVPUOSPQ',
	    'E_PPPV RQQQU RRQRU RSSUS RSRST ROPUSOV RVSOWOOVS',
	    'MWRYSZR[QZRYR[',
	    'MaRYSZR[QZRYR[ R\\Y]Z\\[[Z\\Y\\[',
	    'MkRYSZR[QZRYR[ R\\Y]Z\\[[Z\\Y\\[ RfYgZf[eZfYf[',
	    'JZRRQSRTSSRRRT',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'FjJ[ZF RMFOGPIOKMLKKJIKGMF RcUeVfXeZc[aZ`XaVcU RYZZXYVWUUVTXUZW[YZ',
	    'FvJ[ZF RMFOGPIOKMLKKJIKGMF RcUeVfXeZc[aZ`XaVcU RoUqVrXqZo[mZlXmVoU RYZZXYVWUUVTXUZW[YZ',
	    'MWTFQL',
	    'JZQFNL RWFTL',
	    'G]NFKL RTFQL RZFWL',
	    'MWPFSL',
	    'JZSFVL RMFPL',
	    'G]VFYL RPFSL RJFML',
	    'LXVcR`Nc',
	    'KYUMOSUY',
	    'KYOMUSOY',
	    'E_LMXY RXMLY RKRLSKTJSKRKT RRYSZR[QZRYR[ RRKSLRMQLRKRM RYRZSYTXSYRYT',
	    'MaRYSZR[QZRYR[ RRSQGRFSGRSRF R\\Y]Z\\[[Z\\Y\\[ R\\S[G\\F]G\\S\\F',
	    'I[QFQS RQYRZQ[PZQYQ[ RQYRZQ[PZQYQ[ RMGOFTFVGWIWKVMUNSORPQRQS RMGOFTFVGWIWKVMUNSORPQRQS',
	    'E_JGZG',
	    'OUb`aa^c\\dYeTfPfKeHdFcCaB`',
	    'OUBFCEFCHBKAP@T@YA\\B^CaEbF',
	    'E_N_VW RV_R[',
	    'CaKRKW RRFRK RYRYW RFUKWPU RH[KWN[ RMIRKWI ROORKUO RTUYW^U RV[YW\\[',
	    'LXOTUT',
	    'G][EI`',
	    'KYQSVS RVbQbQDVD',
	    'KYSSNS RNbSbSDND',
	    'ImQYRZQ[PZQYQ[ RMGOFTFVGWIWKVMUNSORPQRQS RcYdZc[bZcYc[ R_GaFfFhGiIiKhMgNeOdPcRcS',
	    'IeQYRZQ[PZQYQ[ RMGOFTFVGWIWKVMUNSORPQRQS R`YaZ`[_Z`Y`[ R`S_G`FaG`S`F',
	    'MiRYSZR[QZRYR[ RRSQGRFSGRSRF R_Y`Z_[^Z_Y_[ R[G]FbFdGeIeKdMcNaO`P_R_S',
	    'KYNMVMPb',
	    'G^NMN[ RUMUXVZX[ RJMWMYNZP',
	    'H\\NQNU RWPWV RPVPPOQOUPV RQPPPNQMSNUPVQVQP',
	    'H\\VQVU RMPMV RTVTPUQUUTV RSPTPVQWSVUTVSVSP',
	    'JZR[RV RWXRVMX RURRVOR',
	    'MWQZQ[R]S^ RRNQORPSORNRP',
	    'OUBFCEFCHBKAP@T@YA\\B^CaEbF Rb`aa^c\\dYeTfPfKeHdFcCaB`',
	    'JZRFRK RMIRKWI ROORKUO RRFRK RWIRKMI RUORKOO',
	    'JZM^WB RNFOGNHMGNFNH RVYWZV[UZVYV[',
	    'E_JSKRNQQRSTVUYTZS',
	    '>fB^B]C[EZOZQYRWSYUZ_Za[b]b^',
	    'E_JSZS RR[RK RLMXY RXMLY',
	    'E_LRMSLTKSLRLT RXYYZX[WZXYX[ RXKYLXMWLXKXM',
	    'D`KFHL RQFNL RWFTL R]FZL',
	    'E_KRLSKTJSKRKT RRYSZR[QZRYR[ RRKSLRMQLRKRM RYRZSYTXSYRYT',
	    'E_LXMYLZKYLXLZ RLLMMLNKMLLLN RRRSSRTQSRRRT RXXYYXZWYXXXZ RXLYMXNWMXLXN',
	    'MWRYSZR[QZRYR[ RRNSORPQORNRP',
	    'E_KRLSKTJSKRKT RRYSZR[QZRYR[ RRKSLRMQLRKRM RYRZSYTXSYRYT',
	    'E_JSZS RR[RK RLXMYLZKYLXLZ RLLMMLNKMLLLN RXXYYXZWYXXXZ RXLYMXNWMXLXN',
	    'CaR\\S]R^Q]R\\R^ RRRSSRTQSRRRT RRHSIRJQIRHRJ',
	    'CaR^S_R`Q_R^R` RRVSWRXQWRVRX RRNSORPQORNRP RRFSGRHQGRFRH',
	    'OU',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'JZQ@S@UAVDVJUMSNQNOMNJNDOAQ@',
	    'NVRDRN RR=Q>R?S>R=R?',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'JZUFUN RQ@NJWJ',
	    'JZV@O@NFPESEUFVHVKUMSNPNNM',
	    'JZNHOFQESEUFVHVKUMSNQNOMNKNFOCPAR@U@',
	    'JZM@W@PN',
	    'JZQFOENCOAQ@S@UAVCUESFQFOGNINKOMQNSNUMVKVIUGSF',
	    'JZVFUHSIQIOHNFNCOAQ@S@UAVCVHUKTMRNON',
	    'I[LHXH RRBRN',
	    'I[LHXH',
	    'I[LJXJ RLFXF',
	    'MWT=S>RAQFQJROSRTS',
	    'MWP=Q>RASFSJROQRPS',
	    'KZODON ROEQDSDUEVGVN',
	    'JZQSSSUTVWV]U`SaQaO`N]NWOTQS',
	    'JZVaNa RNVPURSRa',
	    'JZNTPSSSUTVVVXUZNaVa',
	    'JZNSVSRXSXUYV[V^U`SaPaN`',
	    'JZUYUa RQSN]W]',
	    'JZVSOSNYPXSXUYV[V^U`SaPaN`',
	    'JZN[OYQXSXUYV[V^U`SaQaO`N^NYOVPTRSUS',
	    'JZMSWSPa',
	    'JZQYOXNVOTQSSSUTVVUXSYQYOZN\\N^O`QaSaU`V^V\\UZSY',
	    'JZVYU[S\\Q\\O[NYNVOTQSSSUTVVV[U^T`RaOa',
	    'I[L[X[ RRURa',
	    'I[L[X[',
	    'I[L]X] RLYXY',
	    'MWTPSQRTQYQ]RbSeTf',
	    'MWPPQQRTSYS]RbQePf',
	    'RR',
	    'KZOXQWSWUXVZVa RV`TaQaO`N^O\\Q[V[',
	    'LYV`TaRaP`O^OZPXRWSWUXVZV[O\\',
	    'KYQaO`N^NZOXQWSWUXVZV^U`SaQa',
	    'KYNWVa RVWNa',
	    'LYOXQWSWUXVZV^U`SaRaP`O^O]V\\',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F[XMPMP[X[ RTGRFNFLGKHJJJPKRLSNTUT',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RSBG_ RZBN_',
	    'F[WYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH RR[RM RRQSOTNVMXM',
	    'HZTPMP RM[MFWF RJVRV',
	    'H[LMTM RL[W[ RO[OIPGRFUFWG RLSTS',
	    'D`I[IM RIOJNLMOMQNRPR[ RRPSNUMXMZN[P[[ RWHM`',
	    'G]L[LFX[XF RHV\\V RHP\\P',
	    'GyL[LFTFVGWHXJXMWOVPTQLQ R^MfM RaFaXbZd[f[ RlZn[r[tZuXuWtUrToTmSlQlPmNoMrMtN',
	    'GmX[QQ RL[LFTFVGWHXJXMWOVPTQLQ R`Zb[f[hZiXiWhUfTcTaS`Q`PaNcMfMhN',
	    'F^IFN[RLV[[F RHV\\V RHP\\P',
	    'D`I[IFOFRGTIULUR RONOUPXRZU[[[[F',
	    'I\\W[WF RWZU[Q[OZNYMWMQNOONQMUMWN RRHZH RXaNa',
	    'F[HSQS RHNTN RWYVZS[Q[NZLXKVJRJOKKLINGQFSFVGWH',
	    'G\\L[LF RX[OO RXFLR RLOTO',
	    'JZLFXF RR[RF ROVUR ROPUL',
	    'IoK[RFY[K[ R`b`QaObNdMgMiNjOkQkWjYiZg[d[bZ`X',
	    'G]ITJSLRNSOTQUSTXOYLYIXGVFUFSGRIRLSOXTYVYWXYWZT[',
	    'G\\L[LFTFVGWHXJXMWOVPTQLQ RHL\\L',
	    'F[VGTFQFNGLIKKJOJRKVLXNZQ[S[VZWYWRSR RRCR^',
	    'I[K[RFY[ RHV\\V RHP\\P',
	    'H\\XZU[P[NZMYLWLUMSNRPQTPVOWNXLXJWHVGTFOFLG RRCR^',
	    'HZVZT[P[NZMYLWLQMONNPMTMVN RRJR^',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^J[O[OWMVKTJQJLKIMGPFTFWGYIZLZQYTWVUWU[Z[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'E_ZSJS RNWJSNO',
	    'E_R[RK RNORKVO',
	    'E_JSZS RVWZSVO',
	    'E_RKR[ RVWR[NW',
	    'E_JSZS RVWZSVO RNOJSNW',
	    'E_R[RK RNORKVO RVWR[NW',
	    'E_KLYZ RRLKLKS',
	    'E_YLKZ RRLYLYS',
	    'E_YZKL RRZYZYS',
	    'E_KZYL RRZKZKS',
	    'E_ZSJS RRWVO RNOJSNW',
	    'E_JSZS RRONW RVWZSVO',
	    'E_JWJQPQ RJQMTOUQTSRUQWRZU',
	    'E_ZWZQTQ RZQWTUUSTQROQMRJU',
	    'E_ZSJS RTOPSTW RNWJSNO',
	    'E_R[RK RNURQVU RNORKVO',
	    'E_JSZS RPOTSPW RVWZSVO',
	    'E_RKR[ RVQRUNQ RVWR[NW',
	    'E_JSVS RZOVSZW RNWJSNO',
	    'E_ZSNS RJONSJW RVWZSVO',
	    'E_ZOZW RJSZS RNWJSNO',
	    'E_R[RK RV[N[ RNORKVO',
	    'E_JOJW RZSJS RVWZSVO',
	    'E_RKR[ RNKVK RVWR[NW',
	    'E_N[V[ RR[RK RNWR[VW RNORKVO',
	    'E_NWJSNO RJSWSYRZPYNWM',
	    'E_VWZSVO RZSMSKRJPKNMM',
	    'E_NWJSNO RJSWSYRZPYNWMUNTPTW',
	    'E_VWZSVO RZSMSKRJPKNMMONPPPW',
	    'E_PUJUJO RZWZQTQ RZQWTUUSTQROQMRJU',
	    'E_JSZS RTOPW RNOJSNW RVWZSVO',
	    'E_PWR[VY ROKLTVOR[',
	    'E_V[VOJO RNSJONK',
	    'E_N[NOZO RVSZOVK',
	    'E_VKVWJW RNSJWN[',
	    'E_NKNWZW RVSZWV[',
	    'E_JOVOV[ RZWV[RW',
	    'E_VKVWJW RNSJWN[',
	    'E_OQKUGQ RYRYQXNVLSKQKNLLNKQKU',
	    'E_UQYU]Q RKRKQLNNLQKSKVLXNYQYU',
	    'E_KLYZ RKHYH RRLKLKS',
	    'E_JWZW RJKJS RZSZ[ RZOJO RNSJONK RV[ZWVS',
	    'E_[KUKUQ RMMLNKQKSLVNXQYSYVXXVYSYQXNUK',
	    'E_IKOKOQ RWMXNYQYSXVVXSYQYNXLVKSKQLNOK',
	    'E_ZSJSNO',
	    'E_ZSJSNW',
	    'E_R[RKVO',
	    'E_R[RKNO',
	    'E_JSZSVO',
	    'E_JSZSVW',
	    'E_RKR[VW',
	    'E_RKR[NW',
	    'E_ZWJW RJOZO RVSZOVK RN[JWNS',
	    'E_N[NK RVKV[ RJONKRO RRWV[ZW',
	    'E_JWZW RZOJO RNSJONK RV[ZWVS',
	    'E_ZWJW RJOZO RN[JWNSJONK',
	    'E_N[NK RVKV[ RJONKROVKZO',
	    'E_JWZW RZOJO RV[ZWVSZOVK',
	    'E_VKV[ RN[NK RZWV[RWN[JW',
	    'E_JVZVVZ RZPJPNL',
	    'E_ZVJVNZ RJPZPVL',
	    'E_ZPMP RZVMV RRXVN ROXJSON',
	    'E_MVWV RMPWP RSNQX ROXJSON RUNZSUX',
	    'E_JVWV RJPWP RRNNX RUNZSUX',
	    'E_ZPMP RZVMV ROXJSON',
	    'E_ONO[ RUNU[ RWPRKMP',
	    'E_JVWV RJPWP RUNZSUX',
	    'E_UXUK ROXOK RMVR[WV',
	    'E_MVWV RMPWP ROXJSON RUNZSUX',
	    'E_OXON RUXUN RMVR[WV RWPRKMP',
	    'E_[XOL RW\\KP RSLKLKT',
	    'E_IXUL RM\\YP RQLYLYT',
	    'E_INUZ RMJYV RQZYZYR',
	    'E_[NOZ RWJKV RSZKZKR',
	    'E_ZXOX RZSJS RZNON RQLJSQZ',
	    'E_JXUX RJSZS RJNUN RSLZSSZ',
	    'E_NWJSNO RZUWQTUQQNULSJS',
	    'E_VWZSVO RJUMQPUSQVUXSZS',
	    'E_NXVX RNSVS RR[RK RNORKVO',
	    'E_VNNN RVSNS RRKR[ RVWR[NW',
	    'E_ZSWS RSSQS RMSJS RNOJSNW',
	    'E_R[RX RRTRR RRNRK RNORKVO',
	    'E_JSMS RQSSS RWSZS RVWZSVO',
	    'E_RKRN RRRRT RRXR[ RVWR[NW',
	    'E_ZSJS RJWJO RNOJSNW',
	    'E_JSZS RZOZW RVWZSVO',
	    'E_ZPZVOVOXJSONOPZP',
	    'E_U[O[OPMPRKWPUPU[',
	    'E_JVJPUPUNZSUXUVJV',
	    'E_OKUKUVWVR[MVOVOK',
	    'E_U[O[OWUWU[ RUSOSOPMPRKWPUPUS',
	    'E_W[M[MWOWOPMPRKWPUPUWWWW[',
	    'E_ONUN RW[M[MWOWOPMPRKWPUPUWWWW[',
	    'E_RKR[ RW[M[MWOWOPMPRKWPUPUWWWW[',
	    'E_PPMPRKWPTP RU[O[OSMSRNWSUSU[',
	    'E_PPMPRKWPTP RW[M[MWOWOSMSRNWSUSUWWWW[',
	    'E_JNNNNPUPUNZSUXUVNVNXJXJN',
	    'E_Z[NO RZKJKJ[ RUONONV',
	    'E_JKVW RJ[Z[ZK ROWVWVP',
	    'E_MPRKWPUPUVWVR[MVOVOPMP',
	    'E_JSZS RVWZSVO RTRTTSVQWOWMVLTLRMPOOQOSPTR',
	    'E_V[VK RNKN[ RZOVKRO RRWN[JW',
	    'E_J[Z[ RJKZK RZSJS RVGZKVOZSVWZ[V_',
	    'E_ZSJS RTWTO RNOJSNW',
	    'E_JSZS RPOPW RVWZSVO',
	    'E_JSZS RRORW RNOJSNW RVWZSVO',
	    'E_ZSJS RWWWO RRWRO RNOJSNW',
	    'E_JSZS RMOMW RRORW RVWZSVO',
	    'E_JSZS RPOPW RTOTW RNWJSNO RVWZSVO',
	    'E_NSZS RNWNOJSNW',
	    'E_VSJS RVWVOZSVW',
	    'E_NSVS RNWJSNONW RVWVOZSVW',
	    'I[MLWL RKFR[YF',
	    'HZVHUGSFPFNGMHLKLVMYNZP[S[UZVY',
	    'H[WOVNTMPMNNMOLQLWMYNZP[S[UZVYWWWJVHUGSFOFMG',
	    'I\\WPPP RM[W[WFMF',
	    'I\\WQPQ RMFWFW[M[ RXCL`',
	    'C`G[\\F ROFTFXHZJ\\N\\SZWXYT[O[KYIWGSGNIJKHOF',
	    'I[K[RFY[K[',
	    'I[YFR[KFYF',
	    'C`\\QGQ R\\GOGKIIKGOGSIWKYO[\\[',
	    'C`[CH^ R\\QGQ R\\GOGKIIKGOGSIWKYO[\\[',
	    'E_JSZS RZZPZMYKWJTJRKOMMPLZL',
	    'DaHP]P RHZUZYX[V]R]N[JYHUFHF',
	    'DaI^\\C RHP]P RHZUZYX[V]R]N[JYHUFHF',
	    'E_ZSJS RJZTZWYYWZTZRYOWMTLJL',
	    'E_M[WQ RMZWP RMYWO RMXWN RMWWM RMVWL RMUWK RMTVK RMSUK RMRTK RMQSK RMPRK RMOQK RMNPK RMMOK RMLNK RN[WR RO[WS RP[WT RQ[WU RR[WV RS[WW RT[WX RU[WY RV[WZ RM[MKWKW[M[',
	    'E_Z`ZFJFJ`',
	    'E_ZFZ`J`JF',
	    'E_Z`I`TSIF[F',
	    'E_JSZS',
	    'E_ZWJW RROR_ RJKZK',
	    'E_JSZS RR[RK RRDQERFSERDRF',
	    'G][EI`',
	    'KYID[_',
	    'E_KOYW RR[RK RYOKW',
	    'E_PQRPTQUSTURVPUOSPQ',
	    'E_PQPU RQUQQ RRPRV RSUSQ RTQTU RPTRVTT RPRRPTR RPQRPTQUSTURVPUOSPQ',
	    'IbMTQSS[bB',
	    'IbMTQSS[bB RN@V@RESEUFVHVKUMSNPNNM',
	    'IbMTQSS[bB RUFUN RQ@NJWJ',
	    'E_XPWPUQQUOVMULSMQOPQQUUWVXV',
	    'E_TQVPXQYSXUVVTUPQNPLQKSLUNVPUTQ',
	    'E_JKJ[Z[',
	    'E_ZKJ[Z[',
	    'E_ZKJ[Z[ RPSRUTZT]',
	    'E_Z[JSZK RSYTWUSTOSM',
	    'H\\RbRD',
	    'H\\NUVQ RRDRb',
	    'H\\ODOb RUDUb',
	    'H\\LVXP RODOb RUDUb',
	    'E_[[RKI[',
	    'E_IKR[[K',
	    'E_Z[ZQXMTKPKLMJQJ[',
	    'E_JKJULYP[T[XYZUZK',
	    'H\\L]M_O`Q_R]RISGUFWGXI',
	    'D`H]I_K`M_N]NIOGQFSGTI RP]Q_S`U_V]VIWGYF[G\\I',
	    '@dD]E_G`I_J]JIKGMFOGPI RL]M_O`Q_R]RISGUFWGXI RT]U_W`Y_Z]ZI[G]F_G`I',
	    'H\\L]M_O`Q_R]RISGUFWGXI RRMUNWPXSWVUXRYOXMVLSMPONRM',
	    'D`H]I_K`M_N]NIOGQFSGTI RP]Q_S`U_V]VIWGYF[G\\I RVMYN[P\\S[VYXVYNYKXIVHSIPKNNMVM',
	    '@dD]E_G`I_J]JIKGMFOGPI RL]M_O`Q_R]RISGUFWGXI RT]U_W`Y_Z]ZI[G]F_G`I RZM]N_P`S_V]XZYJYGXEVDSEPGNJMZM',
	    'H\\URXU[R RLSMPONRMUNWPXSXU RL]M_O`Q_R]RISGUFWGXI',
	    'H\\UQXT[Q RL]M_O`Q_R]RISGUFWGXI RLSMPONRMUNWPXSWVUXRYOXMVLS',
	    'H\\UUXR[U RL]M_O`Q_R]RISGUFWGXI RLSMPONRMUNWPXSWVUXRYOXMVLS',
	    'E_KXLYKZJYKXKZ RRLSMRNQMRLRN RYXZYYZXYYXYZ',
	    'E_YNXMYLZMYNYL RRZQYRXSYRZRX RKNJMKLLMKNKL',
	    'JZRXSYRZQYRXRZ RRLSMRNQMRLRN',
	    'E_LXMYLZKYLXLZ RLLMMLNKMLLLN RXXYYXZWYXXXZ RXLYMXNWMXLXN',
	    'E_JSZS RRFQGRHSGRFRH',
	    'E_JSTS RYXZYYZXYYXYZ RYLZMYNXMYLYN',
	    'E_JSZS RLXMYLZKYLXLZ RLLMMLNKMLLLN RXXYYXZWYXXXZ RXLYMXNWMXLXN',
	    'E_JSKRNQQRSTVUYTZS RRXSYRZQYRXRZ RRLSMRNQMRLRN',
	    'E_JSKRNQQRSTVUYTZS',
	    'E_ZSYRVQSRQTNUKTJS',
	    'E_WPYQZSYUWVTUPQMPKQJSKUMV',
	    'E_JSKNLLNKPLQNSXTZV[XZYXZS',
	    'E_RKSLTOSRQTPWQZR[',
	    'E_JSKRNQQRSTVUYTZS RVKN[',
	    'E_ZPJP RZVYWVXSWQUNTKUJV',
	    'E_JVZV RJPKONNQOSQVRYQZP',
	    'E_JVZV RJPKONNQOSQVRYQZP RVKN[',
	    'E_JYZY RJSZS RJMKLNKQLSNVOYNZM',
	    'E_JYZY RJSZS RUPO\\ RJMKLNKQLSNVOYNZM',
	    'E_JYZY RJSZS RJMKLNKQLSNVOYNZM RXGL_',
	    'E_JVKUNTQUSWVXYWZV RJPKONNQOSQVRYQZP',
	    'E_JVKUNTQUSWVXYWZV RJPKONNQOSQVRYQZP RVKN[',
	    'E_JYZY RJSKRNQQRSTVUYTZS RJMKLNKQLSNVOYNZM',
	    'E_JYKXNWQXSZV[YZZY RJSKRNQQRSTVUYTZS RJMKLNKQLSNVOYNZM',
	    'E_ZYJY RZSJS RZMYLVKSLQNNOKNJM',
	    'E_JXLWPVTVXWZX RJNLOPPTPXOZN',
	    'E_JVNVNWOYQZSZUYVWVVZV RJPNPNOOMQLSLUMVOVPZP',
	    'E_ZVJV RJPNPNOOMQLSLUMVOVPZP',
	    'E_JPZP RZVJV RRHQIRJSIRHRJ',
	    'E_JPZP RZVJV RRXSYRZQYRXRZ RRLSMRNQMRLRN',
	    'E_JPZP RZVJV RKJLKKLJKKJKL RYZZ[Y\\X[YZY\\',
	    'E_ZPJP RJVZV RYJXKYLZKYJYL RKZJ[K\\L[KZK\\',
	    'AcNP^P R^VNV RGVHWGXFWGVGX RGNHOGPFOGNGP',
	    'AcVPFP RFVVV R]V\\W]X^W]V]X R]N\\O]P^O]N]P',
	    'E_JPZP RZVJV RPQRPTQUSTURVPUOSPQ',
	    'E_JPZP RZVJV RRJPIOGPERDTEUGTIRJ',
	    'E_JPZP RZVJV RNJOHQGSGUHVJ',
	    'E_JPZP RZVJV RNJRGVJ',
	    'E_JPZP RZVJV RNGRJVG',
	    'E_JPZP RZVJV RRATGOCUCPGRA',
	    'E_JPZP RZVJV RR?NJVJR?',
	    'E_JPZP RYC]C RZVJV R]?[@ZBZJ RM?MJKJIIHGHEICKBMB RQFVFVCUBRBQCQIRJUJ',
	    'E_JPZP RZVJV RMBMJ RMCNBQBRCRJ RRCSBVBWCWJ',
	    'E_JPZP RZVJV RRHSIRJQIRHRJ RN@P?S?U@VBUDSE',
	    'E_JPZP RTMPY RZVJV',
	    'E_JYZY RJSZS RJMZM',
	    'E_JYZY RJSZS RJMZM RXGL_',
	    'E_J\\Z\\ RJPZP RJJZJ RZVJV',
	    'E_ZZJZ RZVJPZJ',
	    'E_JZZZ RJVZPJJ',
	    'E_J]Z] RZWJW RZSJMZG',
	    'E_Z]J] RJWZW RJSZMJG',
	    'E_J]Z] RTTP` RZWJW RZSJMZG',
	    'E_JWZW RTTP` RZ]J] RJSZMJG',
	    '=gRMBSRY RbMRSbY',
	    '=gRMbSRY RBMRSBY',
	    'I[OCPDRGSITLUQUUTZS]R_PbOc RUcTbR_Q]PZOUOQPLQIRGTDUC',
	    'E_JXLWPVTVXWZX RJNLOPPTPXOZN RVKN[',
	    'E_ZMJSZY RVKN[',
	    'E_JMZSJY RVKN[',
	    'E_ZZJZ RZVJPZJ RXGL_',
	    'E_JZZZ RJVZPJJ RXGL_',
	    'E_ZVJPZJ RJZKYNXQYS[V\\Y[ZZ',
	    'E_JVZPJJ RJZKYNXQYS[V\\Y[ZZ',
	    'E_ZVJPZJ RJZKYNXQYS[V\\Y[ZZ RXGL_',
	    'E_JVZPJJ RJZKYNXQYS[V\\Y[ZZ RXGL_',
	    'E_JSZYJ_ RZSJMZG',
	    'E_ZSJYZ_ RJSZMJG',
	    'E_JSZYJ_ RZSJMZG RXGL_',
	    'E_ZSJYZ_ RJSZMJG RXGL_',
	    'E_ZKXNVPRRJSRTVVXXZ[',
	    'E_JKLNNPRRZSRTNVLXJ[',
	    'E_JVRWVYX[Z^ RZHXKVMROJPRQVSXUZX',
	    'E_ZVRWNYL[J^ RJHLKNMROZPRQNSLUJX',
	    'E_J[KZNYQZS\\V]Y\\Z[ RZHXKVMROJPRQVSXUZX',
	    'E_J[KZNYQZS\\V]Y\\Z[ RJXLUNSRQZPRONMLKJH',
	    'E_ZKXNVPRRJSRTVVXXZ[ RVKN[',
	    'E_JKLNNPRRZSRTNVLXJ[ RVKN[',
	    'E_ZMNMLNKOJQJUKWLXNYZY',
	    'E_JMVMXNYOZQZUYWXXVYJY',
	    'E_ZMNMLNKOJQJUKWLXNYZY RVKN[',
	    'E_JMVMXNYOZQZUYWXXVYJY RVKN[',
	    'E_J\\Z\\ RZJNJLKKLJNJRKTLUNVZV',
	    'E_Z\\J\\ RJJVJXKYLZNZRYTXUVVJV',
	    'E_J\\Z\\ RZJNJLKKLJNJRKTLUNVZV RXGL_',
	    'E_Z\\J\\ RJJVJXKYLZNZRYTXUVVJV RXGL_',
	    'E_J\\Z\\ RZJNJLKKLJNJRKTLUNVZV RSYQ_',
	    'E_Z\\J\\ RJJVJXKYLZNZRYTXUVVJV RSYQ_',
	    'E_JKJULYP[T[XYZUZK ROSUS RSUUSSQ',
	    'E_JKJULYP[T[XYZUZK RRRQSRTSSRRRT',
	    'E_JKJULYP[T[XYZUZK RLSXS RRMRY',
	    'E_ZYJYJMZM',
	    'E_JYZYZMJM',
	    'E_Z\\J\\ RZVJVJJZJ',
	    'E_J\\Z\\ RJVZVZJJJ',
	    'E_Z[ZKJKJ[',
	    'E_JKJ[Z[ZK',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RLSXS RRMRY',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RLSXS',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RMNWX RWNMX',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RWFM^',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRRQSRTSSRRRT',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RPQRPTQUSTURVPUOSPQ',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRNRS RMQRSWQ ROWRSUW',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RLUXU RLQXQ',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RNSVS',
	    'E_JKZKZ[J[JK RLSXS RRMRY',
	    'E_JKZKZ[J[JK RLSXS',
	    'E_JKZKZ[J[JK RMNWX RWNMX',
	    'E_JKZKZ[J[JK RRRQSRTSSRRRT',
	    'E_J[JK RJSZS',
	    'E_Z[ZK RZSJS',
	    'E_ZKJK RRKR[',
	    'E_J[Z[ RR[RK',
	    'I[NSVS RNKN[',
	    'I[NVVV RNPVP RNKN[',
	    'E_JVZV RJPZP RJKJ[',
	    'E_JKJ[ RPSZS RPKP[',
	    'E_JKJ[ ROKO[ RTKT[ RYSTS',
	    'E_JKJ[ RPVYV RPPYP RPKP[',
	    'E_J[JK RJSZS RXGL_',
	    'E_JVZV RJPZP RJKJ[ RXGL_',
	    'E_JKJ[ RPSZS RPKP[ RXGL_',
	    'E_JKJ[ RPVYV RPPYP RPKP[ RXGL_',
	    'E_VKXLYNXPVQRRJSRTVUXVYXXZV[',
	    'E_NKLLKNLPNQRRZSRTNULVKXLZN[',
	    'E_JSZYZMJS',
	    'E_ZSJYJMZS',
	    'E_Z[J[ RJQZWZKJQ',
	    'E_J[Z[ RZQJWJKZQ',
	    'BbXQXU RYQYU RZPZV R[Q[U R\\Q\\U RMSLQJPHQGSHUJVLUMSWSXUZV\\U]S\\QZPXQWS',
	    'BbLQLU RKQKU RJPJV RIQIU RHQHU RWSXQZP\\Q]S\\UZVXUWSMSLUJVHUGSHQJPLQMS',
	    'E_JSTSUUWVYUZSYQWPUQTS',
	    'E_JSNS RR[RW RRKRO RZSVS',
	    'I[NFVF RRFR[',
	    'E_J[Z[ RZKRVJK',
	    'E_ZKJK RJ[RPZ[',
	    'E_JKZK RZPR[JP',
	    'E_JKJ[Z[ RJOLOQQTTVYV[',
	    'E_Z[ZKJ[Z[',
	    'Bb_`REE`',
	    'BbEFRa_F',
	    'Bb]`]O\\KZHWFSEQEMFJHHKGOG`',
	    'BbGFGWH[J^M`QaSaW`Z^\\[]W]F',
	    'E_RaJSRFZSRa',
	    'JZRRQSRTSSRRRT',
	    'I[RRTXOTUTPXRR',
	    'E_ZSJS RRXSYRZQYRXRZ RRLSMRNQMRLRN RLMXY RXMLY',
	    'E_JKZ[ZKJ[JK',
	    'E_ZKJ[JKZ[',
	    'E_JKZ[ZKJ[',
	    'E_JKZ[ RRSJ[',
	    'E_ZKJ[ RRSZ[',
	    'E_ZVJV RZPYOVNSOQQNRKQJP',
	    'E_JKMMOOQSR[SSUOWMZK',
	    'E_Z[WYUWSSRKQSOWMYJ[',
	    'E_ZPSPQQPSQUSVZV RZ\\Q\\N[KXJUJQKNNKQJZJ',
	    'E_JPQPSQTSSUQVJV RJ\\S\\V[YXZUZQYNVKSJJJ',
	    'E_U[UTTRRQPROTO[ R[[[RZOWLTKPKMLJOIRI[',
	    'E_OKORPTRUTTURUK RIKITJWMZP[T[WZZW[T[K',
	    'E_RKR[ RL[LSMPNOQNSNVOWPXSX[',
	    'E_JPZP RZVJV RODOb RUDUb',
	    'E_ZMJSZY RYRXSYTZSYRYT',
	    'E_JMZSJY RKRJSKTLSKRKT',
	    '5oJM:SJY RZMJSZY RjMZSjY',
	    '5oZMjSZY RJMZSJY R:MJS:Y',
	    'E_ZSJS RJWZ[J_ RZOJKZG',
	    'E_JSZS RZWJ[Z_ RJOZKJG',
	    'E_ZLJL RZPJVZ\\',
	    'E_JLZL RJPZVJ\\',
	    'E_JPROVMXKZH RZ^X[VYRWJVRUVSXQZN',
	    'E_ZPRONMLKJH RJ^L[NYRWZVRUNSLQJN',
	    'E_JPROVMXKZH RZ^X[VYRWJVRUVSXQZN RXGL_',
	    'E_ZPRONMLKJH RJ^L[NYRWZVRUNSLQJN RXGL_',
	    'E_Z\\J\\ RZVJVJJZJ RXGL_',
	    'E_J\\Z\\ RJVZVZJJJ RXGL_',
	    'E_Z\\J\\ RZVJVJJZJ RSYQ_',
	    'E_J\\Z\\ RJVZVZJJJ RSYQ_',
	    'E_ZVJPZJ RJZKYNXQYS[V\\Y[ZZ RSWQ]',
	    'E_JVZPJJ RJZKYNXQYS[V\\Y[ZZ RSWQ]',
	    'E_J[KZNYQZS\\V]Y\\Z[ RZHXKVMROJPRQVSXUZX RSXQ^',
	    'E_J[KZNYQZS\\V]Y\\Z[ RJXLUNSRQZPRONMLKJH RSXQ^',
	    'E_JSZYZMJS RXGL_',
	    'E_ZSJYJMZS RXGL_',
	    'E_Z[J[ RJQZWZKJQ RXGL_',
	    'E_J[Z[ RZQJWJKZQ RXGL_',
	    'CaR\\S]R^Q]R\\R^ RRRSSRTQSRRRT RRHSIRJQIRHRJ',
	    'CaHRISHTGSHRHT RRRSSRTQSRRRT R\\R]S\\T[S\\R\\T',
	    'Ca\\H[I\\J]I\\H\\J RRRQSRTSSRRRT RH\\G]H^I]H\\H^',
	    'CaHHIIHJGIHHHJ RRRSSRTQSRRRT R\\\\]]\\^[]\\\\\\^',
	    '>`BQ\\Q R\\GOGKIIKGOGSIWKYO[\\[',
	    '>`GQ\\Q R\\M\\U R\\GOGKIIKGOGSIWKYO[\\[',
	    'E_JSZS RZPZV RZZPZMYKWJTJRKOMMPLZL',
	    'C`\\QGQ R\\GOGKIIKGOGSIWKYO[\\[ RR@QARBSAR@RB',
	    'C`GA\\A R\\QGQ R\\[O[KYIWGSGOIKKIOG\\G',
	    'E_JSZS RZGJG RZLPLMMKOJRJTKWMYPZZZ',
	    'C`G`\\` R\\PGP R\\FOFKHIJGNGRIVKXOZ\\Z',
	    'C`HT\\T RHN\\N R\\GOGKIIKGOGSIWKYO[\\[',
	    'DfbQHQ RHGUGYI[K]O]S[WYYU[H[',
	    'Df]QHQ RHMHU RHGUGYI[K]O]S[WYYU[H[',
	    'E_ZSJS RJPJV RJZTZWYYWZTZRYOWMTLJL',
	    'Da]AHA RHQ]Q RH[U[YY[W]S]O[KYIUGHG',
	    'E_ZSJS RJGZG RJLTLWMYOZRZTYWWYTZJZ',
	    'C`GQ\\Q R\\GGGG[\\[',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RZKJ[',
	    'E_JQRWROZU',
	    'E_J[JORGZOZ[J[',
	    'E_NORKVO',
	    'E_VWR[NW',
	    'E_ZKJK RJ[RPZ[',
	    'E_JNZN RJHZH RJ[RSZ[',
	    'H\\RDSETGSIRJQLRNSOTQSSRTQVRXSYT[S]R^Q`Rb',
	    'KYQbQDVD',
	    'KYSbSDND',
	    'KYQDQbVb',
	    'KYSDSbNb',
	    'E_RWR[ RVSZS',
	    'E_RWR[ RNSJS',
	    'E_RORK RVSZS',
	    'E_RORK RNSJS',
	    'E_ZQJQJV',
	    'D`[JZLYPYVZZ[\\Y[UZOZK[I\\JZKVKPJLIJKKOLULYK[J',
	    'E_JSJQLMPKTKXMZQZS',
	    'E_JSJQLMPKTKXMZQZS RJSZS',
	    'E_JMLLPKTKXLZMR[JM',
	    'E_PUJ[ RTKWLYNZQYTWVTWQVOTNQONQLTK',
	    'E_JSZS RR[RK RVRUPSOQOOPNRNTOVQWSWUVVTVR',
	    'E_JWZW RJOZO RNKN[ RVKV[',
	    'E_LPXPZO[MZKXJVKUMUYV[X\\Z[[YZWXVLVJWIYJ[L\\N[OYOMNKLJJKIMJOLP',
	    'E_ZUJUJP',
	    'E_RORSUS RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_M[RVW[ RN[RWV[ RP[RYT[ RS[RZQ[ RU[RXO[ RYMRPKMROYM RJFZFZKYMKTJVJ[Z[ZVYTKMJJJF',
	    'JZVFNFNM',
	    'JZNFVFVM',
	    'JZV[N[NT',
	    'JZN[V[VT',
	    'H\\RbRMSITGVFXGYI',
	    'H\\RDRYQ]P_N`L_K]',
	    'E_JUKTMSRRWSYTZU',
	    'E_ZQYRWSRTMSKRJQ',
	    'E_LKHK RXK\\K RNORKVO',
	    '@dXK^K RFKLKX[^[',
	    'AfJKZ[ RZKJ[ RFKZKbSZ[F[FK',
	    'AcJKZ[ RZKJ[ RFK^K^[F[FK',
	    '9k>VfV R>LfL RCQCL RD[DV REVEQ RFLFG RHQHL RJVJQ RK[KV RKLKG RMQML ROVOQ RPLPG RRQRL RTVTQ RULUG RWQWL RYVYQ RZ[ZV RZLZG R\\Q\\L R^V^Q R_L_G R`[`V R>QaQaL R>[>GfGf[>[',
	    'KYUcOSUC',
	    'KYOcUSOC',
	    '>cZKJ[ RJKZ[ R^KJKBSJ[^[^K',
	    'AcKOKW RR[YW RRKYO RRE^L^ZRaFZFLRE',
	    'H\\PNKX RYNTX RVRUPSOQOOPNRNTOVQWSWUVVTVR',
	    'E_N[J[JW RZSRSJ[ RVRUPSOQOOPNRNTOVQWSWUVVTVR',
	    'E_JSZS RNYVY RVMNM',
	    'E_RPRKNN RZPZKVN RRKJ[R[ZK',
	    'H\\LS[S RRMRY RXP[SXV RVRUPSOQOOPNRNTOVQWSWUVVTVR',
	    'E_ZSJ\\JJZS RJSZS',
	    'E_J[JRZ[J[',
	    'E_JWJ[Z[ZW',
	    'E_VWR[NW',
	    'D`JaZa RJFZF RRFRa',
	    'D`MFWFWaMaMF',
	    'D`IF[F[aIaIF RJPZP RZVJV',
	    'D`IF[F[aIaIF RZSJS RRXSYRZQYRXRZ RRLSMRNQMRLRN',
	    'D`IF[F[aIaIF RRJ[SR\\ISRJ',
	    'D`IF[F[aIaIF RPQRPTQUSTURVPUOSPQ',
	    'D`IF[F[aIaIF RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRbRD',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RPQRPTQUSTURVPUOSPQ',
	    'E_JSZS RZKJ[',
	    'E_JSZS RJKZ[',
	    'D`IaIF[F[aIa[F',
	    'D`[a[FIFIa[aIF',
	    'D`IF[F[aIaIF RZMJSZY',
	    'D`IF[F[aIaIF RJMZSJY',
	    'E_ZSJS RNWJSNO RR[RK',
	    'E_JSZS RVWZSVO RR[RK',
	    'D`IF[F[aIaIF RZSJS RNWJSNO',
	    'D`IF[F[aIaIF RJSZS RVWZSVO',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RLGX_',
	    'E_J[Z[ RR[RK RZaJa',
	    'E_RKX[L[RK RRbRD',
	    'D`IF[F[aIaIF RIKR[[K',
	    'D`IF[F[aIaIF RRKX[L[RK',
	    'E_ZKJK RRKR[ RVRUPSOQOOPNRNTOVQWSWUVVTVR',
	    'E_R[RK RNORKVO RJSZS',
	    'D`IF[F[aIaIF RR[RK RNORKVO',
	    'E_ZKJK RRKR[ RMEWE',
	    'E_R[LKXKR[ RRbRD',
	    'D`IF[F[aIaIF R[[RKI[',
	    'D`IF[F[aIaIF RR[LKXKR[',
	    'E_J[Z[ RR[RK RPQRPTQUSTURVPUOSPQ',
	    'E_RKR[ RVWR[NW RJSZS',
	    'D`IF[F[aIaIF RRKR[ RVWR[NW',
	    'JZJ]Z] RSFQJ',
	    'E_RKX[L[RK RJ]Z]',
	    'E_RJ[SR\\ISRJ RJ]Z]',
	    'E_PQRPTQUSTURVPUOSPQ RJ]Z]',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RJ]Z]',
	    'E_Z[ZQXMTKPKLMJQJ[ RPQRPTQUSTURVPUOSPQ',
	    'D`IF[F[aIaIF RSFQJ',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRPTVORURPVRP',
	    'D`IF[F[aIaIF RRYSZR[QZRYR[ RRNSORPQORNRP',
	    'E_ZKJK RRKR[ RNDOENFMENDNF RVDWEVFUEVDVF',
	    'E_R[LKXKR[ RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'E_RKWZJQZQMZRK RNDOENFMENDNF RVDWEVFUEVDVF',
	    'E_PQRPTQUSTURVPUOSPQ RNIOJNKMJNINK RVIWJVKUJVIVK',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RNDOENFMENDNF RVDWEVFUEVDVF',
	    'E_JKJULYP[T[XYZUZK RRbRD',
	    'E_ZMNMLNKOJQJUKWLXNYZY RRbRD',
	    'E_JSKRNQQRSTVUYTZS RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'E_JMZSJY RNFOGNHMGNFNH RVFWGVHUGVFVH',
	    'E_JSZS RSZS[R]Q^',
	    'E_R[LKXKR[ RJSKRNQQRSTVUYTZS',
	    'H\\QFSFUGVHWJXNXSWWVYUZS[Q[OZNYMWLSLNMJNHOGQF RJPKONNQOSQVRYQZP',
	    'E_JSKRNQQRSTVUYTZS RRbRD',
	    'MWSZS[R]Q^ RRNSORPQORNRP RJ]Z]',
	    'D`IF[F[aIaIF RJPZP RTMPY RZVJV',
	    'D`IF[F[aIaIF RQYRZQ[PZQYQ[ RMGOFTFVGWIWKVMUNSORPQRQS',
	    'E_IKR[[K RJSKRNQQRSTVUYTZS',
	    'E_[[RKI[ RJSKRNQQRSTVUYTZS',
	    'MXRMRXSZU[',
	    'H\\MbMQNOONQMTMVNWOXQXWWYVZT[Q[OZMX',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[ RJ]Z]',
	    'HZLTST RVZT[P[NZMYLWLQMONNPMTMVN RJ]Z]',
	    'MXRMRXSZU[ RJ]Z]',
	    'G]RTRX RMMLNKPKXLZN[O[QZRXSZU[V[XZYXYPXNWM RJ]Z]',
	    'H]YMVWUYTZR[P[NZMYLVLRMONNPMRMTNUOVQWXXZZ[',
	    'IbMTQSS[bB RXL`L',
	    'A_J_F_F[ RJKJ[Z[ RF_OVEQOG',
	    'E_JWNWN[V[VWZW',
	    'E_NSN[J[ RVSV[Z[ RJSJQLMPKTKXMZQZSJS',
	    'E_PQPU RQUQQ RRPRV RSUSQ RTQTU RPTRVTT RPRRPTR RPQRPTQUSTURVPUOSPQ RRbRD',
	    'E_VWR[NW ROEQDSDUEVGVN RVMTNQNOMNKOIQHVH',
	    'BbF[^[ RGLIKKKMLNNNU RUSVTUUTTUSUU R]S^T]U\\T]S]U RNTLUIUGTFRGPIONO',
	    'BbF[N[ RV[^[ RGLIKKKMLNNNU RWLYK[K]L^N^U RNTLUIUGTFRGPIONO R^T\\UYUWTVRWPYO^O',
	    'BbHPDP RJUFX RJKFH R^XZU R^HZK R`P\\P RTTRUPUNTMRMQNNPLRKVKTU',
	    '=_RKR[B[BKRK RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_JKZKZ[J[JK RRbRD',
	    'C_ESUS RQWUSQO RJWJ[Z[ZKJKJO',
	    '@dX[^[ RZO^KZG RF[L[XK^K',
	    'E_KOYW RR[RK RYOKW RRMONMPLSMVOXRYUXWVXSWPUNRM',
	    'E_JSOSR[USZS RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_R[KOYOR[ RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_STJK RJOJKNK RSKTKXMZQZUXYT[P[LYJUJT',
	    'D`KNKROR RYRWPTOPOMPKR RNXMVKUIVHXIZK[MZNX RVXWZY[[Z\\X[VYUWVVX',
	    'E_I[N[NKVKV[[[',
	    'E_I[V[VK RN[NK[K',
	    'E_JKZK RJSRKZSR[JS',
	    'E_Z[J[ RZSR[JSRKZS',
	    'E_JKZK RJSRKZSR[JS RJSZS',
	    'E_Z[J[ RZSR[JSRKZS RJSZS',
	    'E_JVLV RJPZP RQVSV RXVZV',
	    'BbL[FQLGXG^QX[L[',
	    'D`IF[F[aIaIF',
	    'MWTFQL',
	    'AcZSJS RRORK RR[RW RNOJSNW R^[F[FK^K^[',
	    'AcJSZS RRWR[ RRKRO RVWZSVO RFK^K^[F[FK',
	    'BbLHQHQC RLSLHQCXCXSLS RLKJKHLGNGXHZJ[Z[\\Z]X]N\\LZKXK',
	    'BbROJW RZORW RGXGNHLJKZK\\L]N]X\\ZZ[J[HZGX',
	    'H\\XDVGUITLSQR[Rb',
	    'H\\RbRD',
	    'H\\XbV_U]TZSURKRD',
	    'H\\LDNGOIPLQQR[Rb',
	    'H\\RbRD',
	    'H\\LbN_O]PZQURKRD',
	    'H\\XGRGRb',
	    'H\\RbRD',
	    'H\\X_R_RD',
	    'H\\LGRGRb',
	    'H\\RbRD',
	    'H\\L_R_RD',
	    'H\\XDTHSJRNRb',
	    'H\\RDRIQMPOLSPWQYR]Rb',
	    'H\\XbT^S\\RXRD',
	    'H\\RbRD',
	    'H\\LDPHQJRNRb',
	    'H\\RDRISMTOXSTWSYR]Rb',
	    'H\\LbP^Q\\RXRD',
	    'H\\RbRD',
	    'H\\HS\\S',
	    'H\\WDSHRKR[Q^Mb',
	    'H\\MDQHRKR[S^Wb',
	    'E_VbIF\\F',
	    'E_VDI`\\`',
	    '>fC^CYaYa^',
	    '>fCHCMaMaH',
	    '>fC^CYaYa^ RaHaMCMCH',
	    'IbMTQSS[bB',
	    'H\\RbRD',
	    'H\\RbRD',
	    'H\\HG\\G',
	    'H\\HM\\M',
	    'H\\\\YHY',
	    'H\\\\_H_',
	    'E_UFOFO[',
	    'E_U[O[OF',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRbRD',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RZEJE RRERa',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RJaZa RRaRE',
	    'E_RK[[I[RK RRbRD',
	    'E_RK[[I[RK RZEJE RRERa',
	    'E_RK[[I[RK RJaZa RRaRE',
	    'E_JSKRNQQRSTVUYTZS RRbRD',
	    'E_JSKRNQQRSTVUYTZS RZEJE RRERa',
	    'E_JSKRNQQRSTVUYTZS RJaZa RRaRE',
	    'E_JaZa RRaRE',
	    'E_ZEJE RRERa',
	    'E_OFUFU[',
	    'E_O[U[UF',
	    'D`TFQL RMKJKJ[Z[ZKWK',
	    'E_IWN\\NZZZZKTKTTNTNRIW',
	    'E_Z[J[ RJVRKZV',
	    'H\\RbRD',
	    'H\\NQNROTQUSUUTVRVQ',
	    'H\\NQNROTQUSUUTVRVQ RMKWK',
	    'H\\NQNROTQUSUUTVRVQ RW[M[',
	    'CaGQGRHTJULUNTOROQ RUQURVTXUZU\\T]R]Q RGK]K',
	    'CaGQGRHTJULUNTOROQ RUQURVTXUZU\\T]R]Q R][G[',
	    'E_JQJRKTMUOUQTRRRQ RRRSTUUWUYTZRZQ',
	    'E_JUZUZP',
	    'E_JPJUZUZP',
	    'E_RPRU RJPJUZUZP',
	    'E_HO\\O RLUXU RRFRO RT[P[',
	    'E_HS\\S RJMZMZYJYJM',
	    '>fB]C\\FZHYKXPWTWYX\\Y^Za\\b]',
	    '>fbIaJ^L\\MYNTOPOKNHMFLCJBI',
	    '>fB^B]C[EZOZQYRWSYUZ_Za[b]b^',
	    '>fbHbIaK_LULSMROQMOLELCKBIBH',
	    '>fB^FY^Yb^',
	    '>fbH^MFMBH',
	    'E_I[NKVK[[I[',
	    'AcRE^L^ZRaFZFLRE RQLSLVMXOYRYTXWVYSZQZNYLWKTKRLONMQL',
	    'E_JSZS',
	    'E_HXMN\\NWXHX',
	    'E_JSZS RJSKNLLNKPLQNSXTZV[XZYXZS',
	    'E_LMXY RXMLY RPQRPTQUSTURVPUOSPQ',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'E_KKK[ RL[LK RMKM[ RN[NK ROKO[ RP[PK RQKQ[ RR[RK RSKS[ RT[TK RUKU[ RV[VK RWKW[ RX[XK RYKY[ RJKZKZ[J[JK',
	    'E_JKZKZ[J[JK',
	    'E_KLMKWKYLZNZXYZW[M[KZJXJNKL',
	    'E_JKZKZ[J[JK RPPPV RQVQP RRPRV RSVSP RTPTV ROVOPUPUVOV',
	    'E_JWZW RJSZS RJOZO RJKZKZ[J[JK',
	    'E_NKN[ RRKR[ RVKV[ RJKZKZ[J[JK',
	    'E_JWZW RJSZS RJOZO RNKN[ RRKR[ RVKV[ RJKZKZ[J[JK',
	    'E_JKZ[ RN[JW RT[JQ RZUPK RZOVK RJKZKZ[J[JK',
	    'E_J[ZK RJUTK RJONK RP[ZQ RV[ZW RJKZKZ[J[JK',
	    'E_J[ZK RJUTK RJONK RJKZ[ RN[JW RP[ZQ RT[JQ RV[ZW RZUPK RZOVK RJKZKZ[J[JK',
	    'E_PPPV RQVQP RRPRV RSVSP RTPTV ROVOPUPUVOV',
	    'E_OVOPUPUVOV',
	    'E_JXTN RJWSN RJVRN RJUQN RJTPN RJSON RJRNN RJQMN RJPLN RJOKN RKXUN RLXVN RMXWN RNXXN ROXYN RPXZN RQXZO RRXZP RSXZQ RTXZR RUXZS RVXZT RWXZU RXXZV RYXZW RJNZNZXJXJN',
	    'E_JNZNZXJXJN',
	    'E_M[WQ RMZWP RMYWO RMXWN RMWWM RMVWL RMUWK RMTVK RMSUK RMRTK RMQSK RMPRK RMOQK RMNPK RMMOK RMLNK RN[WR RO[WS RP[WT RQ[WU RR[WV RS[WW RT[WX RU[WY RV[WZ RM[MKWKW[M[',
	    'E_M[MKWKW[M[',
	    'E_NNLP RONKR RPNJT RQNIV RRNHX RSNIX RTNJX RUNKX RVNLX RWNMX RXVVX RXNNX RYTUX RYNOX RZRTX RZNPX R[PSX R[NQX R\\NRX RHXMN\\NWXHX',
	    'E_HXMN\\NWXHX',
	    'E_JZJ[ RKXK[ RLVL[ RMTM[ RNSN[ ROQO[ RPOP[ RQMQ[ RRKR[ RSMS[ RTOT[ RUQU[ RVSV[ RWTW[ RXVX[ RYXY[ RZ[RLJ[ RZZZ[ RRK[[I[RK',
	    'E_RK[[I[RK',
	    'E_OUOV RPSPV RQQQV RRORV RSQSV RTSTV RUUUV ROVRPUV RROVVNVRO',
	    'E_ROVVNVRO',
	    'E_KKK[ RLLLZ RMLMZ RNMNY ROMOY RPNPX RQNQX RRORW RSPSV RTPTV RUQUU RVQVU RWSXS RWRWT RJKYSJ[ RZSJ\\JJZS',
	    'E_ZSJ\\JJZS',
	    'E_PPPV RQQQU RRQRU RSSUS RSRST ROPUSOV RVSOWOOVS',
	    'E_VSOWOOVS',
	    'E_KNKX RLNLX RMOMW RNONW ROOOW RPPPV RQPQV RRPRV RSQSU RTQTU RURUT RVRVT RWRWT RXSWS RJNYSJX RZSJYJMZS',
	    'E_ZSJYJMZS',
	    'E_ZLZK RYNYK RXPXK RWRWK RVSVK RUUUK RTWTK RSYSK RR[RK RQYQK RPWPK ROUOK RNSNK RMRMK RLPLK RKNKK RJKRZZK RJLJK RR[IK[KR[',
	    'E_R[IK[KR[',
	    'E_UQUP RTSTP RSUSP RRWRP RQUQP RPSPP ROQOP RUPRVOP RRWNPVPRW',
	    'E_RWNPVPRW',
	    'E_Y[YK RXZXL RWZWL RVYVM RUYUM RTXTN RSXSN RRWRO RQVQP RPVPP ROUOQ RNUNQ RMSLS RMTMR RZ[KSZK RJSZJZ\\JS',
	    'E_JSZJZ\\JS',
	    'E_TVTP RSUSQ RRURQ RQSOS RQTQR RUVOSUP RNSUOUWNS',
	    'E_NSUOUWNS',
	    'E_YXYN RXXXN RWWWO RVWVO RUWUO RTVTP RSVSP RRVRP RQUQQ RPUPQ ROTOR RNTNR RMTMR RLSMS RZXKSZN RJSZMZYJS',
	    'E_JSZMZYJS',
	    'E_JRJT RKUKQ RLPLV RMWMO RNNNX ROYOM RPLPZ RQ[QK RRJR\\ RS[SK RTLTZ RUYUM RVNVX RWWWO RXPXV RYUYQ RZRZT RRJ[SR\\ISRJ',
	    'E_RJ[SR\\ISRJ',
	    'E_RJ[SR\\ISRJ RPRPT RQUQQ RRPRV RSUSQ RTRTT RRPUSRVOSRP',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RPQPU RQUQQ RRPRV RSUSQ RTQTU RPTRVTT RPRRPTR RPQRPTQUSTURVPUOSPQ',
	    'E_RaJSRFZSRa',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_JQKO RKWJU RNLPK RP[NZ RTKVL RVZT[ RYOZQ RZUYW',
	    'E_NLNZ RRKR[ RVLVZ RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RPQRPTQUSTURVPUOSPQ',
	    'E_KOKW RLXP[ RLNPK RLMLY RMYMM RNLNZ ROZOL RPKP[ RQ[QK RRKR[ RS[SK RT[XX RTKT[ RTKXN RUZUL RVLVZ RWYWM RXMXY RYWYO RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RKOKW RLYLM RMMMY RNZNL ROLOZ RP[LX RP[PK RLN RQKQ[ RR[P[LYJUJQLMPKRKR[',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RYWYO RXMXY RWYWM RVLVZ RUZUL RTKXN RTKT[ RXX RS[SK RRKTKXMZQZUXYT[R[RK',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RKOKS RLMLS RMSMM RNLNS ROSOL RPKLN RPKPS RQKQS RRKRS RSKSS RTSTK RXN RULUS RVSVL RWMWS RXMXS RYOYS RJSJQLMPKTKXMZQZSJS',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RYWYS RXYXS RWSWY RVZVS RUSUZ RT[XX RT[TS RS[SS RR[RS RQ[QS RPSP[ RLX ROZOS RNSNZ RMYMS RLYLS RKWKS RZSZUXYT[P[LYJUJSZS',
	    'E_SSSK RTKTS RTKXN RUSUL RVLVS RWSWM RXMXS RYSYO RZSRSRK RPKTKXMZQZUXYT[P[LYJUJQLMPK',
	    'E_QSQ[ RP[PS RP[LX ROSOZ RNZNS RMSMY RLYLS RKSKW RJSRSR[ RT[P[LYJUJQLMPKTKXMZQZUXYT[ RYWYO RXMXY RWYWM RVLVZ RUZUL RTKXN RTKT[ RXX RS[SK RRKTKXMZQZUXYT[R[RK',
	    'E_KOKW RLYLM RMMMY RNZNL ROLOZ RP[LX RP[PK RLN RQKQ[ RR[P[LYJUJQLMPKRKR[',
	    'E_YWYO RXMXY RWYWM RVLVZ RUZUL RTKXN RTKT[ RXX RS[SK RRKTKXMZQZUXYT[R[RK',
	    'E_FDFb RGbGD RHDHb RIbID RJDJb RKbKD RLbLW RLDLO RMXMb RMNMD RNbNY RNDNM ROZOb ROLOD RPbPZ RPDPL RQZQb RQLQD RRbRZ RRDRL RSZSb RSLSD RTbTZ RTDTL RUZUb RULUD RVbVY RVDVM RWXWb RWNWD RXbXW RXDXO RYbYD RZDZb R[b[D R\\D\\b R]b]D R^D^b R_bEbED_D_b RKTKRLONMQLSLVMXOYRYTXWVYSZQZNYLWKT',
	    'E_FRFD RGNIJ RGDGN RHLHD RIDIK RJJJD RJJMG RKDKI RLHLD RMHQF RMDMH RNGND ROPOS RODOG RPSPP RPGPD RQPQS RQDQG RRSRO RRGRD RSPSS RSFWH RSDSG RTSTP RTGTD RUPUS RUDUG RVGVD RWGZJ RWDWH RXHXD RYDYI RZJZD R[J]N R[D[K R\\L\\D R]D]N R^R^D ROQROUQ RNSOPROUPVSNS RFSFRGNIKJJMHQGSGWHZJ[K]N^R^S_S_DEDESFS R^T^b R]X[\\ R]b]X R\\Z\\b R[b[[ RZ\\Zb RZ\\W_ RYbY] RX^Xb RW^S` RWbW^ RV_Vb RUVUS RUbU_ RTSTV RT_Tb RSVSS RSbS_ RRSRW RR_Rb RQVQS RQ`M^ RQbQ_ RPSPV RP_Pb ROVOS RObO_ RN_Nb RM_J\\ RMbM^ RL^Lb RKbK] RJ\\Jb RI\\GX RIbI[ RHZHb RGbGX RFTFb RUURWOU RVSUVRWOVNSVS R^S^T]X[[Z\\W^S_Q_M^J\\I[GXFTFSESEb_b_S^S',
	    'E_FRFD RGNIJ RGDGN RHLHD RIDIK RJJJD RJJMG RKDKI RLHLD RMHQF RMDMH RNGND ROPOS RODOG RPSPP RPGPD RQPQS RQDQG RRSRO RRGRD RSPSS RSFWH RSDSG RTSTP RTGTD RUPUS RUDUG RVGVD RWGZJ RWDWH RXHXD RYDYI RZJZD R[J]N R[D[K R\\L\\D R]D]N R^R^D ROQROUQ RNSOPROUPVSNS RFSFRGNIKJJMHQGSGWHZJ[K]N^R^S_S_DEDESFS',
	    'E_^T^b R]X[\\ R]b]X R\\Z\\b R[b[[ RZ\\Zb RZ\\W_ RYbY] RX^Xb RW^S` RWbW^ RV_Vb RUVUS RUbU_ RTSTV RT_Tb RSVSS RSbS_ RRSRW RR_Rb RQVQS RQ`M^ RQbQ_ RPSPV RP_Pb ROVOS RObO_ RN_Nb RM_J\\ RMbM^ RL^Lb RKbK] RJ\\Jb RI\\GX RIbI[ RHZHb RGbGX RFTFb RUURWOU RVSUVRWOVNSVS R^S^T]X[[Z\\W^S_Q_M^J\\I[GXFTFSESEb_b_S^S',
	    'E_JSJQLMPKRK',
	    'E_ZSZQXMTKRK',
	    'E_ZSZUXYT[R[',
	    'E_JSJULYP[R[',
	    'E_JSJQLMPKTKXMZQZS',
	    'E_ZSZUXYT[P[LYJUJS',
	    'E_KZK[ RLYL[ RMXM[ RNWN[ ROVO[ RPUP[ RQTQ[ RRSR[ RSRS[ RTQT[ RUPU[ RVOV[ RWNW[ RXMX[ RYLY[ RZ[ZKJ[Z[',
	    'E_YZY[ RXYX[ RWXW[ RVWV[ RUVU[ RTUT[ RSTS[ RRSR[ RQRQ[ RPQP[ ROPO[ RNON[ RMNM[ RLML[ RKLK[ RJ[JKZ[J[',
	    'E_YLYK RXMXK RWNWK RVOVK RUPUK RTQTK RSRSK RRSRK RQTQK RPUPK ROVOK RNWNK RMXMK RLYLK RKZKK RJKJ[ZKJK',
	    'E_KLKK RLMLK RMNMK RNONK ROPOK RPQPK RQRQK RRSRK RSTSK RTUTK RUVUK RVWVK RWXWK RXYXK RYZYK RZKZ[JKZK',
	    'E_PQRPTQUSTURVPUOSPQ',
	    'E_JKZKZ[J[JK RK[KK RLKL[ RM[MK RNKN[ RO[OK RPKP[ RQ[QK RJ[JKRKR[J[',
	    'E_JKZKZ[J[JK RYKY[ RX[XK RWKW[ RV[VK RUKU[ RT[TK RSKS[ RZKZ[R[RKZK',
	    'E_JKZKZ[J[JK RYLYK RXMXK RWNWK RVOVK RUPUK RTQTK RSRSK RRSRK RQTQK RPUPK ROVOK RNWNK RMXMK RLYLK RKZKK RJKJ[ZKJK',
	    'E_JKZKZ[J[JK RKZK[ RLYL[ RMXM[ RNWN[ ROVO[ RPUP[ RQTQ[ RRSR[ RSRS[ RTQT[ RUPU[ RVOV[ RWNW[ RXMX[ RYLY[ RZ[ZKJ[Z[',
	    'E_JKZKZ[J[JK RR[RK',
	    'E_RK[[I[RK RRUQVRWSVRURW',
	    'E_J[RL RJZJ[ RKXK[ RLVL[ RMTM[ RNSN[ ROQO[ RPOP[ RQMQ[ RRKR[ RRK[[I[RK',
	    'E_Z[RL RZZZ[ RYXY[ RXVX[ RWTW[ RVSV[ RUQU[ RTOT[ RSMS[ RRKR[ RRKI[[[RK',
	    'C`OFTFXHZJ\\N\\SZWXYT[O[KYIWGSGNIJKHOF',
	    'E_JKZKZ[J[JK RRKRSJS',
	    'E_JKZKZ[J[JK RR[RSJS',
	    'E_JKZKZ[J[JK RR[RSZS',
	    'E_JKZKZ[J[JK RRKRSZS',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRKRSJS',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RR[RSJS',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RR[RSZS',
	    'E_PKTKXMZQZUXYT[P[LYJUJQLMPK RRKRSZS',
	    'E_JKJ[ZKJK',
	    'E_ZKZ[JKZK',
	    'E_J[JKZ[J[',
	    'E_JKZKZ[J[JK',
	    'E_KKK[ RL[LK RMKM[ RN[NK ROKO[ RP[PK RQKQ[ RR[RK RSKS[ RT[TK RUKU[ RV[VK RWKW[ RX[XK RYKY[ RJKZKZ[J[JK',
	    'E_OVOPUPUVOV',
	    'E_PPPV RQVQP RRPRV RSVSP RTPTV ROVOPUPUVOV',
	    'E_Z[ZKJ[Z[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'Pf',
	    'PfUUYZ',
	    'PfWTYT[V[XYZWZUXUVWT',
	    'PfZKXS R^K\\S',
	    'PfYFUISMSQUUZXZUXTXRZQ[R[L]N^L^FaIcMcQaU\\X',
	    'PfZJYMVQ RYM`M\\T RZR]V',
	    'PfbHTWWK^R',
	    'PfWG_GcMcS_XWXSSSMWG',
	    'PfaD[OaZ',
	    'PfUD[OUZ',
	    'PfaD[OaZ R^DXO^Z',
	    'PfUD[OUZ RXD^OXZ',
	    'PfbD^D^R',
	    'PfT[X[XO',
	    'PfbDbH^H^PZPZDbD',
	    'PfT[TWYWYO]O][T[',
	    'Pf^DbDaIaOaUbZ^Z^D',
	    'PfTDXDX[T[UVUITD',
	    'PfUIaI RUNaN R[N[Y',
	    'PfUJaJaNUNUJ RURaRaVUVUR',
	    'PfbD_H_VbZ',
	    'PfTDWHWVTZ',
	    'Pf\\DbDaIaOaUbZ\\Z\\D',
	    'PfTDZDZ[T[UVUITD',
	    'PfbD]F]XbZ R`E`Y',
	    'PfTDYFYXTZ RVEVY',
	    'PfbD]D][b[ R`D`[',
	    'PfTDYDY[T[ RVDV[',
	    'PfTOXL^RbO',
	    'Pf^EbK RYE]K',
	    'PfWDTJ R[DXJ',
	    'PfXTTY R]TYY',
	    'PfWI_I RWL_L R[L[S RWSXU^U_S RVNXNYPXRVRUPVN R^N`NaP`R^R]P^N RTNRNRSTSVX`XbSdSdNbN',
	    'Pf[F[Y',
	    'PfXJXU R]F]X',
	    'PfVHVX R[J[V R`G`X',
	    'PfaK^SUZ RWOaV',
	    'PfZHVN]O_R_U]XYXWTWR_M',
	    'Pf[M[P RTPbP',
	    'Pf[J[M RTMbM RTQbQ',
	    'Pf[I[L RTLbL RTPbP RTTbT',
	    'PfXLWOTR RWObO R`O_VV[ RVQ[S_Y',
	    'PfT\\W\\Y^YaWcTcRaR^T\\',
	    'PfTAWAYCYFWHTHRFRCTA',
	    'Pf_AbAdCdFbH_H]F]C_A',
	    'Pf_\\b\\d^dabc_c]a]^_\\',
	    'PfgOjOlQlTjVgVeTeQgO',
	    'PfgKjKlMlPjRgRePeMgK RgTjTlVlYj[g[eYeVgT',
	    'PfSQVMYQ\\M_QbM',
	    'Pf]DWP]Z',
	    'Pf]I`L R`HcK R]DWP]Z',
	    'Pf_GWY',
	    'Pf_MaP RbMdP R_GWY',
	    'PfVH_X',
	    'PfWG_GcMcS_XWXSSSMWG RWK_K RWO_O R[O[U',
	    'PfUFZY R[FUY R\\FaY RaF\\Y',
	    'PfULaL R[E[Y',
	    'PfTLbL RXEXY R^E^Y',
	    'PfTNbN RWGWVUY R[I[V R_H_Y',
	    'PfXI^N\\O RXP^U',
	    'PfUJaJaWUWUJ RaJUW',
	    'PfTLWHZM]JbW',
	    'PfTIVI RXIZI R\\I^I R`IbI RbK RbMbO RbQbS RbUbW R`W R^W\\W RZWXW RVWTWTU RTSTQ RTOTM RTKTI RWM[K]N`L RWQ_Q RWT_T R\\PYV',
	    'PfUHaHaYUYUH R_JWW RWJ_W',
	    'Pf',
	    'PfVO]O RYLYTZY R\\QXYWYVXVUZR^R`U`W]Z',
	    'PfTI^H RYEXPZY R]LZUVZTUXP^NaRaX][',
	    'PfVPVWX[ZX R]Q`W',
	    'Pf^J`NaS RTHTOUTWZZT',
	    'PfZJ]L RWO]N_Q^VZ[',
	    'PfXD]F RUM\\J_M_S]XXZ',
	    'PfZN]P RXR^RX[ R[W]W][`[',
	    'PfYE]H RWL^KV[ RYU]R]Z`Z',
	    'PfUQ[Q RXNX[UYUWZT^T`W`Y[[ R]O`R',
	    'PfTJ[I RWEWYTWTSZP^QaS`X[Y R^HaL',
	    'PfSLZK\\OZZWY RXDTZ R]IaQ',
	    'PfSLZK\\OZZWX RXDTY R]H`Q R`JbM RcIeL',
	    'PfVI^G RUNaK RYD]SZS RVTVWXY\\Z',
	    'PfVI^G RUNaK RYD]SZS RVTVWXY\\Z R_DaG RbCdF',
	    'Pf]EXO]Z',
	    'Pf]EXO]Z R_IaL RbHdK',
	    'PfZLaL RVDUKURUVVYXS R^E_M_S^W\\Z',
	    'PfZLaL RVDUKURUVVYXS R^E_M_S^W\\Z RaEcH RdDfG',
	    'PfWG^G[J RWPUUWZ`Z',
	    'PfWG^G[J RWPUUWZ`Z R`DbG RcCeF',
	    'PfTK`I RYE_R[Q RVRVVXY]Z',
	    'PfTK`I RYE_R[Q RVRVVXY]Z R_DaG RbCdF',
	    'PfWEWVXYZ[][`YaU',
	    'PfWEWVXYZ[][`YaU R\\L^O R_KaN',
	    'PfSJaJ R]E]S\\WX[ R\\OZMYMWPWRYSZS\\Q',
	    'PfSJaJ R]E]S\\WX[ R\\OZMYMWPWRYSZS\\Q R`DbG RcCeF',
	    'PfTMbL R^E^S\\R RWGWZ`Z',
	    'PfTMbL R^E^S\\R RWGWZ`Z R`EbH RcDeG',
	    'PfWF_EXM RTNaL R_M[PYRYU[X^Z',
	    'PfWF_EXM RTNaL R_M[PYRYU[X^Z RaDcG RdCfF',
	    'PfTI[I RYDTY RZN`N RYSZW\\YaY',
	    'PfTI[I RYDTY RZN`N RYT[YaY R_GaJ RbFdI',
	    'PfTI^I RXDUSYO]O_R_V\\YX[',
	    'PfTI^I RXDUSYO]O_R_V\\YX[ R^E`H RaDcG',
	    'PfTO]M`NaR_UYX',
	    'PfSL]I`JaMaP`S]VWX',
	    'PfSL]I`JaMaP`S]VWX R`EbH RcDeG',
	    'PfTIaG R_H[KYPYV[Y^Z',
	    'PfTIaG R_H[KYPYV[Y^Z R`CbF RcBeE',
	    'Pf_KWQUSUWWZ_Z RWDXIZN',
	    'Pf_KWQUSUWWZ_Z RWDXIZN R_GaJ RbFdI',
	    'PfTIZI RXDTU R_HbL R]L]X[YXXXT[SaX',
	    'PfZHaH RUDTLTRUYWR RZSZW[XaX',
	    'PfUGXW R[EXTUXSUTQWK]JaNaV^Z\\ZZW\\TbY',
	    'PfWEWZ RTJWJWK RSVZK^IaJbNaU^Y\\YZXZU]TbX',
	    'Pf[GWWTTTLVH[F_GbLbRaV\\Y',
	    'PfYIaI R^E^YYXYT\\SaW RUETKTQUYVR',
	    'PfYIaI R^E^YYXYT\\SaW RUETKTQUYVR R`EbH RcDeG',
	    'PfYIaI R^E^YYXYT\\SaW RUETKTQUYVR RbDcDdEdFcGbGaFaEbD',
	    'PfSKYGUNUTVXXZ[Y\\W]S^M]GbO',
	    'PfSKYGUNUTVXXZ[Y\\W]S^M]GbO R`EbH RcDeG',
	    'PfSKYGUNUTVXXZ[Y\\W]S^M]GbO RbEcEdFdGcHbHaGaFbE',
	    'PfYE]H RZK[Q]U\\YYYWW RVPTX R_QaW',
	    'PfYE]H RZK[Q]U\\YYYWW RVPTX R_QaW R_DaG RbCdF',
	    'PfYE]H RZK[Q]U\\YYYWW RVPTX R_QaW R`DaDbEbFaG`G_F_E`D',
	    'PfTRYKbS',
	    'Pf^J`M RaIcL RTRYKbS',
	    'Pf_I`IaJaK`L_L^K^J_I RTRYKbS',
	    'PfYF`F RYL`L R^F^ZZYZW\\UbX RUETKTQUZWS',
	    'PfYF`F RYL`L R^F^[ZYZW\\UbX RUETKTQUZWS RaCcF RdBfE',
	    'PfYF`F RYL`L R^F^[ZYZW\\UbX RUETKTQUZWS RcCdCeDeEdFcFbEbDcC',
	    'PfTH`H RVM^M R[D[YXYUWVUZT`W',
	    'PfVG\\GZNVXTUTRWP[PbT R_K_Q^U[Y',
	    'PfSHYH RWEVVXZ^Z_V_Q RVRUTTTSRSPTNUNVP R]IaN',
	    'PfUHYX R[FYVVZSVSRWM[K_MbRaW]Z',
	    'PfYDXVYZ[[^[`ZaV`P RTI\\I RUO\\O',
	    'PfUR]N`O`Q_S\\T RVL[[ RYK[M',
	    'PfSO_KaLbP_S\\S RUG[[ RYE\\H',
	    'PfTLTVWP\\MaQaV]YYV R]J]R[[',
	    'PfULUXXP[M_MbPbU_W\\WZU R]J]Y[[',
	    'Pf[N[ZVYVVYU`X R[Q_Q',
	    'Pf[E[[WZUXUVWTaY R[K`J',
	    'PfYE]H RXIVUYQ]P`S_XY[',
	    'Pf^E^R]VYZ RWEVJVNWQYN',
	    'PfWF_EVS[O`OaRaW][Y[XWZU^Y',
	    'PfWEWZ RTJXIWJ RSV\\I_I`L_S_YbU',
	    'PfXG^FWT[O`OaRaW^YZZ',
	    'PfWIWZ RULXLWN RUU[M^MaNaT_W[Y',
	    'PfWEWZ RTKXJWL RSVYN[K_KaMbQ`U[Y',
	    'PfWG]FWZUUVQZM^NaQaX][ZY[V_X',
	    'PfXE^EVN R\\K`M`QZTWRXP[QTY RVWXW[Z R]W_WaY',
	    'PfUH^H RZDUSYM[O\\U R`NWUWXZ[_[',
	    'Pf[EU[ZQ\\Q^[_[bV',
	    'PfXD]F RUM\\J_M_S]XXZ R`FbI RcEeH',
	    'PfUO\\N]P\\YYW RYJUY R^LaQ',
	    'PfYP`O RUKTQTUVZWW R]L]V\\X[[',
	    'Pf',
	    'Pf',
	    'Pf^E`H RaDcG',
	    'PfaDbDcEcFbGaG`F`EaD',
	    'PfSEUH RVDXG',
	    'PfTDUDVEVFUGTGSFSETD',
	    'PfYI`P\\R',
	    'PfYI`P\\R R^G`J RaFcI',
	    'PfZJ`J R[EUW RXP^P`S_X\\[YZ',
	    'PfTLbL RTTbT',
	    'PfVK`K_N]Q R[N[RZUXX',
	    'PfTGbGaI_L\\N RZJZQZSYVW[',
	    'Pf[P[Z R^J\\NYQVS',
	    'Pf[L[[ R`E^H[LWOTQ',
	    'PfZHZL RVOVL_L_O^S]U[WXY',
	    'Pf[D[H RUOUHaH`N_Q]U[XWZ',
	    'PfWL_L R[L[W RVW`W',
	    'PfUIaI RTWbW R[I[W',
	    'PfWO`O R]K]Z[Y R\\O[RYUVX',
	    'PfUKbK R^D^Z[Y R]K[PXSTW',
	    'PfUJaJ`Y]W RZCZJZOYSWVUY',
	    'PfUJaJ`Y]W RZCZJZOYSWVUY R_EaH RaCcF',
	    'PfVL^J RUSaP RYD]Z',
	    'Pf]E_G R`DbF RVL^J RUSaP RYD]Z',
	    'PfZDYIWLUP RZH`H`L_P]T[WWZ',
	    'PfZDYIWLUO RZGaG`L_P]T[WWZ R`DbF RcCeE',
	    'PfWKbK RXDWHUMTP R]K]P\\SZVWZ',
	    'PfWKbK RXDWHUMTQ R]K]P\\SZVWZ R^G`I RaFcH',
	    'PfUIaIaWUW',
	    'Pf`FbH RcEeG RUIaIaWUW',
	    'PfTKbK RWEWR R_D_K_O^S]U[XYZ',
	    'PfTKbK RWEWR R_D_K_O^S]U[XYZ RaDbF RdCeE',
	    'PfWFZI RULXO RUYZW]U_SbK',
	    'PfWFZI RULXO RUYZW]U_SbK R_GaI RbFdH',
	    'PfUF^F]L[PYSWVTY R[Q]T`Y',
	    'PfUF^F]L[OYSWVTY R[Q]T`Y R`EbG RcDeF',
	    'PfULbJ^R RYEYXaX',
	    'Pf_EaG RbDdF RULbJ^R RYDYXaX',
	    'PfUFWL R`F`L_P^S\\VWY',
	    'PfaG`L_P^T\\WXZ RaDcF RdCfE RUGWM',
	    'PfXL]R RYDXHWLUP RYH`H_L^P]T[WXZ',
	    'PfXL]R RYDXHWLUP RYH`H_L^P]T[WXZ R`EbG RcDeF',
	    'PfTNbN R_E]FZGVH R\\G\\M[QZUYWVZ',
	    'PfTNbN R_E]FZGVH R\\G\\M[QZUYWWZ R`DbF RcCeE',
	    'PfULWQ RZK[P R`L`Q_T\\WYY',
	    'PfUGWN RYF[L R`G`M_Q]U[WXY',
	    'PfUGWN RYF[L R`G`M_Q]U[WXY RaEcG RdDfF',
	    'PfWG_G RTMbM R[M[RYVVZ',
	    'Pf`EbG RcDeF RWG_G RTMbM R[M[RYVVZ',
	    'Pf[D[Z R[MaR',
	    'Pf_KaM RbJdL R[D[Z R[MaR',
	    'PfTLbL R[D[K[QZTXWVZ',
	    'PfUKaK RSWcW',
	    'PfXM_W RWF`F_L^P\\UZWVZ',
	    'PfYD]G R[P[[ R]QaU RVH`H^L\\OYRTU',
	    'Pf_F^L]QZUVY',
	    'Pf^JbV RYJXOVSTV',
	    'Pf^JbV RYJXOVSTV R_HaJ RbGdI',
	    'Pf^JbV RYJXOVSTV R`GaGbHbIaJ`J_I_H`G',
	    'PfUFUYaY R`J\\MYNVO',
	    'PfUFUYaY R`J\\MYNVO R`HbJ RcGeI',
	    'PfUFUYaY R`J\\MYNVO RaFbFcGcHbIaI`H`GaF',
	    'PfUH`H`M_R]UZWVY',
	    'PfUH`H`M_R]UZWVY RaFcH RdEfG',
	    'PfUH`H`M_R]UZWVY RbEcEdFdGcHbHaGaFbE',
	    'PfTRYJbV',
	    'Pf]K_M R`JbL RTRYJbV',
	    'Pf_K`KaLaM`N_N^M^L_K RTRYJbV',
	    'PfUKaK R[E[ZXY R^OaW RWOVRUTTW',
	    'PfUKaK R[E[ZXY R^OaW RWOVRUTTW R`GbI RcFeH',
	    'PfUKaK R[E[ZXY R^OaW RWOVRUTTW RaFbFcGcHbIaI`H`GaF',
	    'PfTJaJ_N]Q[S RWPZS[U]X',
	    'PfWFaJ RWM_P RUT`Y',
	    'Pf[FUY_W R]PaZ',
	    'Pf`E_J]OZSXUTX RXKZM]Q`U',
	    'PfVG`G RSOcO RZGZY`Y',
	    'PfUOaL^R RXI[Z',
	    'PfXE[Z RTMaI`L_O^Q',
	    'PfXL^L^V RVVaV',
	    'PfVI`I_W RSWcW',
	    'PfWL_L_XWX RWR_R',
	    'PfUHaHaXUX RUPaP',
	    'PfVG`G RTLaLaQ_T\\WXZ',
	    'PfXEXP R_E_M_Q]U\\WYZ',
	    'PfWGWOVSUVTY R[E[Y]W_TaP',
	    'PfWEWX[W^U`SbO',
	    'PfUHUV RUHaHaVUV',
	    'PfVPVJ`J_P]UYZ',
	    'PfUGUN RaG`M_Q^U\\XYZ RUGaG',
	    'PfWJbJ RWJWS RSScS R]D]Z',
	    'PfVIaI]P R[L[W RSWcW',
	    'PfVM`M RUF`F`L`O_S]VZXVZ',
	    'PfUHYL RUXZW]T_QaJ',
	    'Pf[D[H RUOUHaH`N_Q]U[XWZ RaEcG RdDfF',
	    'PfWM_M^Y\\X R[IZNYSWX',
	    'PfYMaM RYIXMWPUS R_M^Q]T\\WZZ',
	    'PfaEcG RdDfF RUGUN RaG`N`Q^U\\WYZ RUGaG',
	    'Pf`GbI RcFeH RWJbJ RWJWS RSScS R]D]Z',
	    'Pf`FbH RcEeG RVIaI]P R[L[W RSWcW',
	    'PfVM`M RUF`F`L`O_S]VZXVZ RaEcG RdDfF',
	    'PfZP\\P]Q]S\\TZTYSYQZP',
	    'PfSPcP',
	    'PfWK^U',
	    'Pf\\M^O R_KaM RWK^U',
	    'PfVF`F`Y',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'F^K[KFYFY[K[',
	    'PoROlO',
	    'PoRFlF RX[`[ R`F`[',
	    'PoRFlF R^[e[ RZFVQ RWNiNfZ',
	    'Po\\D\\[j[jW RSOkK',
	    'PoR[l[ R_D_[',
	    'PoRFlF R_F_[',
	    'PoRGlG R[UU[ R^LYW R_G\\T',
	    'PoRFlF R\\F[PXVT[ R\\NiMiTg[`[',
	    'PoRIlI RWLZS^WcYj[ RbDbLaT\\XYZS[',
	    'PoTFjF RVPhP RR[l[',
	    'Po^MjM RR[l[ R^D^[',
	    'PoRElE R^E^[ R^KjQ',
	    'PoSGlG RfFf[ RXVR[ RYFYPXV',
	    'PoRElE R`H`[ RaGXPRS RaLiOlS',
	    'PoYHiH RRTlT R\\[d[ RYCWNgNeZ',
	    'PoRElE RURjR Rb[h[ RUJUU R_E_R R_KkKjRi[',
	    'PoRElE R_KjK Rb[i[ R_E_P RUQlQi[ RVITR',
	    'PoROlO RR[l[ R[FZNX[ RUFgFf[',
	    'PoaXhX RR[l[ R`PcT RXUSW R^NVV RVK`P RaH^O RTFkFkNiX',
	    'PoTGjG RRLlL ReS`X RYQhQbW R^C\\JXQ RYWeZ',
	    'PoWLgL RWTgT RR[l[ RWEW[ RWEgEg[',
	    'PoSFkF RR[l[ R`I`Y RcMfOkS RaF`IXQRS',
	    'PoRJlJ R_RgR RWYkY RWDW[ R_C_T RgDgT',
	    'PoRKlK RWYgY RWCW[ R_C_Y RgCg[',
	    'PoWNkN RR[l[ RWGW[ RdNd[ RhEWG',
	    'PoRElE Re[j[ RSKS[ RSKkKk[ R_F^PZUVV R^NgV',
	    'PoR[l[ R[D[[ RcDc[ RTKXS RjKfS',
	    'PoR[l[ RhTlX RaT\\X RYL\\O^T ReMiV RXOTW RcN_W RdHgS RYEWS RdDbR',
	    'PoRGlG RUPjP R[[`[ R`K`[ ReSlY RYKUO RXTRZ R\\CYL',
	    'Po`VkV RTV]V RR[l[ RkJgN RbJ`NhN`V R]J\\N RVKTO[OTV RZDVM RfDbL',
	    'PoYX_X RS[k[ R_J_X RVEgEdG_J RRL[LXQSV RjJgMbN R`JbOgTlV',
	    'PoSEkE RUJiJ RRPlP RZZjZ RXZhZ RRZeZ R_E_P ReTl[ R[PWZ',
	    'PoSI[I RcVkV RR[l[ R[C[W RcCcV RlRkV RkHbL R[QRS',
	    'PoRElE RYVeV RTKT[ RYOYX R_E_V ReOeX RTKjKj[e[',
	    'PoUJiJ RRPlP R_YiY R[YeY RUZaZ RTF_E_P RdTjY R]QUZ RiD_E',
	    'PoRElE RSJ[J RSS[S RcSlS RSJSS RcEc[ RcJkJkS RZUWYR[ R[E[QZV',
	    'PoRElE Re[j[ RSKS[ RSKkKk[ R[E[O]R RfRhV RcEcOgS RZNWV RbO_V R[LXT R[IZR RcIbR',
	    'PoTEkE RUNlN R\\E\\N RcEcN RVHXL RhGgL RUNUUTZ',
	    'PoSJkJ RR[l[ R\\J\\[ RbJb[ RXDZH RfCdH RUNXW RiNfW',
	    'PoSGkG RRPlP RWPW[ RVIYM RgJcM R_C_Q`TdXk[ RjRbU R_YVZ',
	    'Po_C_[',
	    'PoWFWU RgCg[ R\\TUU R`TXU RcT[U RgS^T',
	    'Po_L_[ RUMRN R_CVL R_CgLlN',
	    'Po_L_[ RTEZK RRDVF RkDeJ',
	    'PoaCa[ RUG[J RaRRU',
	    'PoRRlR RRHRT R_C_[ RRHlHlS',
	    'PoSLdL RRRdR R[G[[ RlWk[ RREgEgQiZ',
	    'Po_C_[ RlQcTUW RiFUI RiL`NUO RfTRW',
	    'PoSGkG RUNiN RRTlT R_C_[',
	    'PoRQ[Q RcQlQ RRGRT RcDc[ RlGlS RZUVYR[ R[D[QZV',
	    'PoSMjM RRVlV RRPRX RSGSL R_C_[ RSGjGjL RRPlPlW',
	    'PoTLkL RRUlU RRPRV RTFTL RcCc[ RTFkFkL RRPlPlV R\\C[UXYR[ R\\N[W',
	    'Po^GlG R\\ZkZ RRGRW RWDW[ R\\P\\[ RdPdY R\\PkPk[ RbJfM R_C[L',
	    'PoRJlJ RTPjP RSUkU RWEWJ R\\C\\J R_P_[ RbCbJ RfEfJ RYL[P ReKcP',
	    'PocVi[ RTN^RaUfX',
	    'PoSPX[ RkPgY',
	    'Po]C]K[RYVT[ RUO`U RTIfIf[l[mV',
	    'PoROlO R]HbM RWEWPVUT[ RWEhEh[b[',
	    'Poc[j[ RaOfT RVDYH R[STZ R^C^MZV RSJlJlRj[ R^F]Q',
	    'PoSIjI RURiR RR[l[ R_I_[ R]DaH',
	    'PoSIkI RRSlS ReCe[ R]LaP RYDYQXWS[',
	    'PoRElE RY[][ Rg[j[ RTJT[ RTJ]J][ RaJa[ RaJjJk[ RWOYS ReOgT',
	    'PoRIkI RWPgP RSUjU R_L_[ RVDYG R]C_G RgDcIhNlP R[IVORP',
	    'PobDbR]WT[ RbHaT',
	    'PoREVN]UeYl[',
	    'PoSF`F RdTk[ RaMfW R`GdU',
	    'PoVF[QaWj[ RgFbQ\\WR[',
	    'PoZFYPXUR[ RTFeFdMmMmRj[d[',
	    'PoRXU[ RZKjS RjDfLdO[YV[',
	    'PoaOcThYlZ RYIcI_RXXR[ R[DXKRP',
	    'Po_LbShYlZ R_L[TWYR[ RVGSP R_H\\T R`D^PRP',
	    'PobPlP R\\PhP RXPaP RRQ\\Q R][k[ R]G][ RkWk[ RhD_FSH',
	    'Po_YkY R\\ZgZ RWZdZ RUZaZ RdRl[ R`M^Q R^R[UVZ R^DTN RbJ_O',
	    'Po_C`I RVFYMcWl[ RhEdN\\VR[',
	    'PoURY[ RiPd[ RlIRM',
	    'Po_C_I RWRSZ RTJjJ_UYW RVU[Zd[m[',
	    'PocMgM RRUhU Rd[j[ R^D\\F RWFhFgL RWFWPlPj[',
	    'PoWIlI R]NjN R]TkT R]H][ RXHRN RZCWI',
	    'PoRPlP RVHYN RhHeN RiD_EUF R_F_[X[',
	    'PoVTTZ RiD_FUF RTMiMaTXX RVV[Zb[l[ R_F_L',
	    'PoWFaF RTOkO R[[_[ R`H`[ RfSlY RXSRY RVFUO RhD_E',
	    'Po_F_[ RVWRY R[RUX R_HcR RiIeMbPgVlX RZJWNRO R_FaO RhDUG',
	    'PoWE`E RWKjK RRTlT RWEWT RdKdT RZXS[ RhD`D',
	    'PoWE`E RWKjK RRTlT RWEWT RdKdT RcVjZ RcD^E RhD`D',
	    'PoRJlJ RdPd[ RYWV[ RaJfOlR RTF^E\\KWPRR RZQYX RfD^E',
	    'PoUIlI RUMlM ReYjY RXQXY RUEaEa[ RXQjQjY RUEURSZ RdD^E RiDaE',
	    'PoRIlI RRPYP RdYlY RZLZY ReLeX RlVlY RkOdQ RYURW RgD_ETF R_F_[',
	    'PoTIkI RRNlN RTSkS RXIXS RfIfS R`SbVlZ R^T\\URZ RiD_EUF R_E_[',
	    'PoRIkI ReKdSkRkS R_QeWlZ RjMdN R_QYWRZ RSMZLZRRR RhD_EUF R_E_[',
	    'PoRFgF^MYQRYR[k[lV',
	    'PoS[j[ RSDSZ RlVj[',
	    'PoRJlIiT',
	    'PobTgT RZ[j[ RYDYZ RlVk[ RcKSN RfIVN RhIYM RiIiNhT',
	    'Po\\D\\MZSXVT[ RSKeKe[l[lW',
	    'PoWHlH RXCWHRL RWMgM[SVYV[k[lX',
	    'Po`C`V RSNjIjOhTdT RXFX[k[lV',
	    'Pod[j[ RWJ`O RgRRW RRFlFlUj[',
	    'PoVKdK R^RjR RVRcR RiITR R`D]HUK RlNfU`XU[ RaXR[ RfJ`K',
	    'PoRV\\V Ra[k[ RRIRY RWDWV R\\I\\X RaCa[ RlVk[',
	    'Poc[k[ RcDc[ RSJYQ R\\T_[ RXCUL RWTTY R\\JWU RlVk[ R[YSZ R^XWY',
	    'PoRElE RT[k[ R^E^O RaHiL RlWk[ RbRTX RTQfQZT',
	    'PoXF_F RUF]F RRG[G RU[k[ RVHYL RWVTY RkWk[ RfHdL RROeOVW RiD^F',
	    'PoeXjX R^C^[ RTHgHgP RgEkF RRPlPkX',
	    'PoaLjL R^LgL RZLdL RUL_L RSM\\M RV[j[ RdCdO RjXj[ RVG\\J RZD_G RTQeQVX',
	    'PoTM_M RRM]M RTY_Y Rc[k[ RTQT[ RTQ_Q_[ RcDc[ R\\H^J`N RXCUM RlVk[',
	    'PoWI_I RRZ]Z Rc[k[ RRPR[ RWCWP RRP]P][ RdDdZ RlUk[',
	    'Po^MjM R^UjU RS[k[ R^E^W RdEdU R^EjEkWk[ RRF[FRX',
	    'PocNlN RZO]O RV[k[ RcFcN RcFlFlN RkXk[ RWKSO RbSVX RURfRZU RYCWL RSF_G^O',
	    'PoSDkD RUKgK RROlO RT[k[ RUDUO RUGgGgK RlXk[ RTSfRUX',
	    'PoSElE RYNiN RU[k[ RZHZN RXIiIiN RkWk[ RaSUY R\\EYHRK RURgRYV',
	    'PofNjN RT[k[ RSESL RaEaP RaEjEjM RkXk[ RZMTP R]UTY R[JRL RTRgRUW R[GZM R\\DRE',
	    'PoRJlJ RZP_P RU[k[ RTE`E_O RWGZI RgFdI RkXk[ R^TTY RTRfRWW RgD_E',
	    'PoRRlR RXI]K RTMZO RaVlZ RTEkEiJ R_T\\XXYR[ RaJ_U',
	    'PoRKaK RTZ_Z RTRT[ RZEZR R_DRF RdDd[l[lW RTR_R_[',
	    'PoV[j[ R^FkFiI RjXj[ R_D[I RdJgMlO RTEYG RbJ_MZO RTQeQVX RcFcIaL RZKRM',
	    'PoTGTK RXGYJ R_F]K R^DRE RRT_T RTM_MYQY[U[ RcDc[k[lV',
	    'PoRG]G R`IlI RU[k[ RgIgQ RkXj[ RXCXPTP RUSfSVW R`E`J_N RjD_E R]JRL',
	    'PoSG`G RRNaN RST`T Rc[k[ RYCYG RYNY[ RdCdZ RUHVM R]H\\M RlVk[',
	    'PoRFlF RTKjK RTMjM ReQiQ RT[k[ RTHTQ R_C_P RTHjHjP RlXk[ RcDhD RTSfSUX',
	    'PoU[j[ RcCcK RgElI RkXj[ RWKSO R^EYJ RTCXE RRGVH R^TUY R_KbK_K RUSfSWW ReMYO',
	    'Po_LlL R`TkT RdWhW RV[k[ RUEUZ RYDYV RaK`S RaEaM RdNeQ RdFfJ RlWk[ R]IRN R]I]S[S RaEjEjQhW',
	    'PoRG_G RSN^N RSR^R RRV_V Rc[k[ RSJSR RYCYJ RYRY[ RSJ^J^R RcCcZ RlVk[',
	    'PoaIlI RUM^M RV[k[ RVH^H^L RhIhQ RkXk[ R_TUY RUSfSWW RVEVJTO RbEbHaN R^DUE RjDaE',
	    'PoRF^F RXQ[Q RV[j[ RXCYF ReDjI RjXj[ ReD_J R_UVY RbJhK RaNiQ RUSdSYW RVFTP RVK\\J[Q',
	    'PoZFkF R[JjJ RYOlO RU[k[ RcEcO RaCcF RkXj[ RTDWE RRHVI RVNRO RTSfSVW',
	    'PoVEdE RRHlH RYNhN RYQhQ RV[k[ RYKYQ R^C^H RVLhLhQ RkXj[ RiEaISM RUTfSVX',
	    'PoRG^G RaHlH RTN]N RTR]R RRV_V RTJTR RXCXJ RXRX[ RTJ]J]R RcC_L RaNjNbUa[l[lW',
	    'PoRS_S Rc[k[ RYSY[ RcCcZ R]P`T RUMXP RYJUM R\\LUS R]U_Y RYFZI R^E`I RUDRJ RUUSZ RlUk[ R^CUD',
	    'PoWLiL RWOiO RTUjU RTXjX RTQTZ RWIWO RWIiIiO RYEcE_H RZCVHRI RUQjQjX R`I`[m[mY',
	    'PoRF]F RaHlH RSM\\M RSP\\P RSS]S RRW]W R`[k[ RSJSP RXSX[ RXCXJ RSJ]J]P RlWl[ ReRaU`Z R`MiMcS RbC_L',
	    'PoVS^S RXV^Y Rd[k[ R\\[`[ RTNT[ RZFZH RTN`N`[ RdDd[ R_EbH RXL^N RTERJ RYNWR RUJ_JZL RWU]UYYVZ RlVk[ R_CTE',
	    'PoSHbH R\\QcQ R\\WcW Rf[k[ R[C[N R`O`[ RfDf[ RUDWG RUUXVS[ R`D^G RYHaL RVOST RURZQWX R\\S\\W RZHWKRM RlUl[',
	    'PoRFaF RSI`I RTO^O RTU_U Rd[k[ RZCZI RTR_R_U RRLRMaL`O ReDe[ RVWVY RTRTU RlUk[ R\\V[ZR[ R`YU[',
	    'PoT[_[ R_D_[',
	    'PoRFlF RRFmF_M_[X[',
	    'PoZ[`[ R`K`[ RWHRN RWIkIiN RYCWJ',
	    'PoRNlNfS RYHeM RUEhEeG_J R_N_\\Z\\',
	    'PoROlO RTUiU R[CTJ RZFdF^J R_K_\\Y\\ RVKiKiV',
	    'PoSFlF RRQlQ RUViV RZ[_[ R_C_[ RUMiMiX RfHeK RWHYK',
	    'PoRFlF RVLiL RRSlS RTWiW RVIVL RVIiIiL RUPiPiX R_C_[Z[',
	    'PoUHiH RR[l[',
	    'PoUFiF RRLlL RY[_[ R_L_[',
	    'PoTFjF RROlO RY[_[ R_F_[',
	    'PoUEiE RRKlK R_[e[ RZKVS RXQhQf[',
	    'PoTDjD RRKlK R^[e[ R\\DXQgPe[',
	    'PoUFiF RRNlN RdSkZ R\\OWZhX',
	    'PoSFkF RWUeU RR[l[ RZFWV RYLfLeQd[',
	    'PoUFiF RRLlL RdLd[ RYUS[ RZLYU',
	    'PoTFjF RR[l[ RUOgOg[ R]GY[',
	    'PoSIlI RRRlR ReCe[ RYDYRXVS[',
	    'PoVFhF RTMjM RVThT RR[l[',
	    'PoTLjL RURiR RR[l[ RTFTL R_C_L RjFjL',
	    'PoSEkE RR[l[ RWJhJhWWWWJ RWPhP',
	    'PoSFkF RR[l[ R]ObQ R[TbW R[FWW RZLgLd[',
	    'PoSElE RR[l[ R[E[[ RbEb[ RTLXT RjLfS',
	    'PoYI^I RVUiU RR[l[ RTGTQ RYCYP RiGaI RRQ^P RbDbPlPmM',
	    'PoSFkF RTTjT RR[l[ RTKTU R[F[[ RbFb[ RTKjKjU',
	    'PoaQkQ RRQ]Q RUUiU RR[l[ RhNkQ RTIgHiJ R\\CZH R[N]R RcFgG RWKTQ RdKaQ',
	    'PoSFkF RR[l[ R[F[LULUT[T[[ RcFcLjLjU RjTcTc[',
	    'PoSFkF RbKjK RTUZU RR[l[ RTLTW RTLZLZU ReRjW RhRbW RcLfT RjLfT R_X[X R\\F[J`J`S_X',
	    'PoR[l[ R_O_[',
	    'PoRIlI R_C_I RWJW[k[',
	    'PoRHkH R_C_G RXLXTWWS[ RXLeLf[l[mV',
	    'PoRIlI RdId[ R^D`F RWVS[ RYIYPXV',
	    'PoRGlG R_C_G RdJgKkO RZN[R`WeYl[ RdNbR^VYYR[ R[JWMRP',
	    'PoRGlG R_D_GXN RaUhYkZ RdK`OTU RiOcT[XR[ RXN\\Q',
	    'PoSHlH R_C_H RgMiOlT RWMUQRT R\\I[RZWU[ RdId[^[',
	    'PoTGkG RUNlN RZI[L R^DaF RfIdM RUNUSSZ',
	    'PoRFlF RWNhN RWIWN R`D`F RWIhIhN RTRjR`U`[Y[',
	    'PoRGlG RUSiS RUZiZ RULU[ R_L_Y RULiLi[ R^C_F',
	    'PoRFlF R^PbP RbFbP R^C`F RfIkN RWIRN R[LWP ReVhYl[ RSSeSfX R[F[M',
	    'PoRFlF RVMhM RRVlV RVIVM R_D_F RVIhIhM RUQiQ_T_[Z[',
	    'PoRGlG RVRhR RVKVQ R_C_G RVKhKhQ RfUlZ RXURZ R_R_\\Y\\',
	    'PoRFlF RVMhM RVTiT RVIVM R_C_F RVIhIhM RSPlPlT RSPSU R_T_\\Y\\',
	    'PoRFlF RWMhM RWIWM R_C_F RWIhIhM RSPkPkT R[SYWXYS[ RSPST RdTd[l[lX',
	    'PoRGlG RWNgN RUUiU RUZiZ RUQU[ RWJWN R_C_G RUQjQj[ RWJgJgN',
	    'PoRGlG RVNhN RVRhR RY[_[ RVJVR R_R_[ RVJhJhR RfUkZ RXUSY R_C_G',
	    'PoSGkG R^PiP R^ViV R[YlY RXLX[ R^J^V R^JiJiV R_C`G RWMRR RYHWN',
	    'PoTGjG RRNlN RRRkR R_N_[ ReHcM RXHZL R_QdWlZ R_QZWRZ R_C_G',
	    'PoRFlF RVMhM R`VlV R\\WhW RUWbW RRW^W R^[k[ RSPSS RVIVM RUTYT^S^[ R_C_F RVIhIhM RSPkPkS RlYl[ RfR\\S',
	    'PoSEkE RWKgK RVPgP RURhR Rd[l[ RRMRP RWHWK R^N^R RSVdVe[ RWHgHgK RRMkMkP RlXk[ R]U[XXZR[',
	    'PoSFkF RSJ\\J RURXR RXGXQ RYUY[ RiQlPlR R^CaF RbM_P R_MeO RgTbV R^RcWfYkZ RcGaN R_RYURV R\\MSN RaYXZ R^JhJiQ',
	    'PoRFlF RYNeN RUPiP RWUgU RWYgY RR[l[ RUHUP RWRWY RYKYN R_C_F RYKeKeN RWRhRhY RUHiHiP',
	    'PoTFlF RTJkJ RWQlQ RXTiT R]J][ RdId[ RXNiNiT R[G]L R_C`F RcTgWlY R]TYXUY RTJTTSZ RdGdI',
	    'PoRF\\F R^MkM RTM[M RSP\\P R^RkR R\\VlV RT[W[ RTITM RWSW[ RTI[I[M R^H^R RdHd[ R^HkHkR R`DcF RiCgH RWCXF R[QWR R[URV',
	    'PoSEkE R]JaJ ReKiK RUNYN RdNiN RXVfV RXYfY RR[l[ RRQRT RUHUQ RXSX[ R[H[Q R]L]O R_C_E R[HcHcQ RXSfSf[ RdHiHiQ RRQkQkT RYHUH RaL]L RaLaO]O RUKZK',
	    'Po_E^PZVVYR[ R^PdUiYm[',
	    'Po\\I\\[ RVNRP R`DUO',
	    'PoS[k[ R`IcQgUlW R^J[QWURW R_EbP R_E[P',
	    'PoVORP R_D^GTP R_D`GhNlP RZUcZ',
	    'PoR[l[ RRFR[ RcPjV R]QWW R`HdR R_K[S R`D]Q',
	    'Po]ZkZ RXHX[ R]GlG^T\\X RWIRN RZCVJ RlTkY',
	    'PoZMlM RXHX[ RcCc[ RWJRN RYCVJ',
	    'Po\\HkH RZYlY RWIW[ RZCWJRN',
	    'Poe[j[ RWHW[ R[JlJlU R`V[[ RVJRN RYCUK RbQ^Y RlMlTj[ RcCcKaU',
	    'Po[FlF R`[e[ RWHW[ RfFf[ RVJRN RYCUL',
	    'PoUEkE RUEURSZ RaI`Q^VZZW[ R`RdWhYl[',
	    'PoXIX[ RWJRN R^JbTgYl[ RZCVK RjIeR_XZ[ R]G`Q R[GjGgO',
	    'PoYHY[ RbCb[ RXIRO RbLkQ R[CXJ',
	    'PoWHW[ RXDVJRN RaCaL_R\\XY[ RZJhJh[l[lW',
	    'PoWHW[ RVJRN RYCVK R]RY[ R^FgFgKiX R_I\\V',
	    'Pog[l[ RWHW[ R^EhEh[ RlVl[ RVJRN RYCVK R^E^T[[ R^N]X',
	    'PoYLeL R_CgKlN RUQhQc[ R_CVLRN',
	    'PodMd[ R_CgKlN R_CVLRN RZMZSXWT[',
	    'Po^GdJgLiM R_NbUeXk[ R_D]IZLTO R_O\\UYXS[',
	    'Pod[j[ RWHW[ RWIRN RYCVJ R^R[Z R[EhEgMlMlSj[ R_E_L]V RgHgO',
	    'PohVl[ RXN\\R]U R`UZ[ RePiY RWRSZ RbO^X RdKgV RXMUX RcK`V RcGfT RYFWU RcGaS',
	    'PoXHX[ RgPl[ RZDXIRO RaD\\ZjX',
	    'PoSQkQ R_K_[ R_CfJlM R^CWKRM',
	    'PoY[g[ RYMY[ RhWh[ R_DeJlM R_DYJRM RePYS',
	    'Po_DfLlO R\\ObQ R_DVMRO RXWeZ',
	    'PoXMfM R`UeU RY[h[ RYMY[ R_DfKlN RiWh[ R`DXLRN RfNeU',
	    'PoYOlO RXIX[ RZDWJRO R\\FkFgJcKc[^[',
	    'PoZLlL R[ZlZ RXHX[ RcDcZ RZDWJRO',
	    'PoWIW[ RdCdU RYDVJRN RZOkIkTiU R^F^[k[lX',
	    'PoZIlI RXHX[ RWJRN R]M`TcXj[ RZCWJ RdCdPbU^YY[ RdKcS',
	    'Po[JlJ RWIW[ R]O`QaT RZDWJRO RhDh[a[',
	    'Po\\XlX RXIX[ R\\K\\[ RdDdX RlKl[ RZDWJRN',
	    'PoUYiY RUPU[ R_K_Y RiPi[ R_CfJlM R^CWKRM',
	    'Pob[k[ RWHW[ RbFbZ RlWk[ RWJRN RYCVK RiE[G RfOZP RiN\\P RkN_P',
	    'Po\\FkF R[ZlZ RWIW[ RcFcZ RWJRO RYDUM',
	    'PoVQhQ RT[j[ R_Q_[ R_CiN R^ETO',
	    'Pod[j[ RWHW[ R`U[[ RWIRN RYCVJ R]KZR RbP_X R[FlFlRj[ RcFcKaT',
	    'PoZNlN RWHW[ RdFd[ RVJRN RYCUK RjE[G',
	    'Po\\FkF RZNlN RXIX[ RcFc[ RWIRN RZCWJ',
	    'Po^HlH R][k[ RWHW[ RlWk[ ReP\\Y R^MhMcR RVJRN RYCVK R`C\\L',
	    'Pod[i[ RWHW[ R_MdR RWIRN RYCVJ R`C[N R^HlHlQj[',
	    'PoXIX[ RgEjH R[DXJRN R[MjK RbDbKeYj[lW',
	    'PoYLeL RdYhY R]Q][ RUQhQhY R_CfJlM R_CWKRM',
	    'PoVEVV RdSl[ RZTRX R\\G`M RhEgNdT_YY[',
	    'Po^[b[ RWHW[ RcDc[ RYDTM RiKlU R\\JZS',
	    'Po^GiG RSMkM R^C^M R_RdXk[ R_P]VZXT[',
	    'Po[GkG R\\PjP RZZlZ RXIX[ RWIRN RZCWJ',
	    'PocMkM R[ZlZ RWHW[ RcCcZ RWIRN RYCVK',
	    'PoWHW[ RVJRN RbDdI R]JbTfYl[ RYCVK RiJeS`XY[ R\\E`R RjEgQ',
	    'PobYkY R^YgY RZYcY RWHW[ RVJRN R]KZN RhSl[ RYCVK RaC]L ReJ^Y',
	    'Pog[l[ RWHW[ R\\H\\[ RcDlDl[ R^D`H RXDSO',
	    'Pob[g[ RWHW[ R^VY[ RVJRN R\\J^OcTlX RYCVK RaP]X R[EjEjOiQh[ RbG`T',
	    'Po]EkE RXIX[ R_LbThYl[ R]LkLgTbY][ R]E]Q[Y RYDVJRN',
	    'Po_ScS RWHW[ RcCcR RVJRN RhHlO RYCVK R^HZO RiRdX\\[',
	    'PogVlV RXHX[ R\\G\\V RdFd[ RdFlFlV RbD\\G RbUZV RYDWJRN',
	    'Po^LgL RWHW[ RbCiJlK RVJRN RbDZK R[QiQd[ RYCVK',
	    'Po[RlR RWHW[ R[I[T RcDc[ R[IlIlT RYDUKRN',
	    'Po\\LbL Rd[k[ RWHW[ R\\D\\Z ReDeZ RXDTN RjKdN RlVl[ RbXYZ',
	    'Po_KlK R_RlR Rg[l[ RWHW[ R_ElEl[ RWJRO RYDUM R_E_R^Y',
	    'Po\\HkH RYPlP RWHW[ RcHc[ RVJRN RXCUL R^CZL',
	    'Po]JkJ RZQlQ RWHW[ RcCc[ R^E[M RYDUKRN',
	    'PoWHW[ RgMg[ RbDkL RVJRN RaEZM RYCUK R^M^R]X',
	    'PoYNlN RWHW[ ReUk[ R]YY[ RaT[Z RVJRN RbEbOgX RYCUL RbP_W RbLeV RbKaT RjD^F',
	    'Po[HkH R[NjN RZTlT RXIX[ RcDc[ RWIRN RZDWJ',
	    'PoWNhN RWTgT RW[h[ RWNW[ R_DeKlN R_DXKRN',
	    'Po[PlP R\\ZjZ RXHX[ RdFdZ RjD]G RZCXIRN',
	    'Po[FkF R\\OkO RZZlZ RXIX[ RcFcZ RWIRN RZDWJ',
	    'Poc[g[ RWHW[ RhRg[ R\\NiNiY R_W[[ RVJRN R\\JYN R`DfDjL RYCUK R^F[M RaO_X',
	    'Po[ElE RZ[l[ RWHW[ RWJRN RYCVJ R_KiKhS R_E]ThRgZ',
	    'PoZHlH Rc[h[ RXIX[ RcCcH R^VZ[ RWIRN RZCWJ R`R]Y RaOjNjSh[ RaJ_V',
	    'PobYjY R]YfY RZZcZ RWHW[ RiJlN RWIRN RhTk[ RaEfDjM RYCVJ R^E[M RaL^Y',
	    'Po_RhR RS[k[ RWPW[ R_K_[ R_DhLlN R_DVMRO',
	    'PoZHlH RfXjX RWHW[ R\\M\\X RcCc[ R\\MjMjW RVJRN RYCVK',
	    'Po\\EjE RZMlM R^[c[ RWHW[ RcMc[ RcJfL RkOgS RVJRN R_HdJ RYCVK RiFbI',
	    'Po[LkL RfYjY RWHW[ RcEc[ R[EkEkK RXDTM R[KZRlRkY',
	    'PoZHkH R[SiS Rc[k[ RWIW[ R[K[S RcCc[ RjKjV RlXk[ RVJRN RYCUL',
	    'Pod[i[ RWHW[ R_QZV RdU^[ RVJRN RhLbX RXDTL R_C[M RcH^S RhHeT R^IlHlPj[',
	    'PogLlL R[PiP RWIW[ R^DgDgL RVJRN R^JZN R]P^SbWj[ RYCUL RiQdWZ[ R^D]K',
	    'Po`[h[ RWHW[ R`I`[ RVJRN RiMlT R_DeG RYCVK R[MYU RhVh[',
	    'PoZHlH Rg[l[ RWHW[ RcCcH R^MgMh[ RVJRN RYCVK R[ZYZ RlWl[ R^M^R]W',
	    'PoYLlL R[SjS RWHW[ R[EjEjT RYDWIRN RaFaNaT[[',
	    'PoWHW[ RVJRN R_McUgYl[ RYCVK R]TZ[ R^MkMhTcY^[ R[EhEgM R^E^N\\X',
	    'Po\\ElE ReSjS R`[k[ RWHW[ RaJaZ R`JjJjS RlWk[ RVJRN RYCUK R\\E\\RZY',
	    'Po[FkF RYZlZ RWHW[ R[NiNiZ RbF^Z RYDVJRO',
	    'PoZHlH RWHW[ RcCcN R]O_SdXl[ RZNjNhRcWZ[ RYDVJRN',
	    'PoZLlL RWHW[ RgEjH RYDVJRN RcDcLaS^W[[ RcLfUl[',
	    'PoWIW[ RgEiH RiObVZZ RZDWJRN RZLjK RaDaKdVh[k[lW',
	    'PoYKkK RXHX[ RbCb[ RZDWJRN RbK^RZW RcKgRlW',
	    'Po]MlM RWHW[ RgMg[ RWIRN RYCVJ R]F]P\\X RjD]F',
	    'Po[FlF RWHW[ RcJc[ RgMlR R]PYR RdH[Q RWJRN RYDUL',
	    'Pog[l[ RWHW[ R[E[I R_JgJgZ R[EkEkI RlWl[ R^WZ[ RVJRN RYCUL R_J_Q^W',
	    'Po[IkI RZPlP RXIX[ ReUj[ RWIRO RcCcQfX RZCWJ RcP`V]YZ[ RcLbU',
	    'PoZHlH RWHW[ RVJRN R_C_OaTfXl[ RYCVK R^QZ[ R_NiNfUbY][ R_M\\X R_H]U',
	    'PogVl[ RYQ^V RVWR[ RbV][ RdOhX R_CfJlM RYQUY RdP`Y R_CWKRM RYMVW RdMbW',
	    'PoYJlJ RdZkZ RWIW[ ReMeZ RkVkZ RfDiH R^WZ[ RVJRN RYCUL R`Q\\Y RaDaK_U',
	    'PoWHW[ RdUjZ RaU[Z RVJRN RcCcPfW RkIhO RYCUK RcO_X R]I[P RcMdU RbLaT',
	    'PoXLfL RSQkQ RUZhZ RdTj[ R_CeJlM R\\QXZ R^CXJRM',
	    'Po\\EkE R\\ZlZ RWHW[ R\\E\\Z R_IfQ^W RWJRN RiHeQ RYCVK RjUjV',
	    'PogOlO RZSlS RWHW[ RcOc[ R[GgGhN RlKlO RWIRN RYCVK R`J]NZO RaC_K',
	    'Po\\FjF RZNlN RWHW[ RaN]ZjY RZCWJRN RiVk[',
	    'PoRTkT R_J_[ RXLZQ R_DaGlK RfLdR R_C\\GRK',
	    'PoZGkG R[LkL RWIW[ RcCc[ RVJRN RYCUK RkXgY RYQlQkY',
	    'Po[GkG RZLlL RWHW[ RbWf[ R`VdX R^QjQcX RVJRN RYCVK RbC^Q',
	    'Po[GlG R\\PkP RZVlV RWHW[ RdKd[ RVJRN RYCUL R`D]O',
	    'Po\\EkE R]OlO Rb[g[ RWIW[ RhEh[ RVJRN RYCUL RhN`VZX R^G]N',
	    'PoeZkZ RWHW[ R^E^R ReSeZ R^EiEiR RbU]Z RWJRN RYCVK RlVkZ RcHcPbU',
	    'Po^HlH Rd[j[ RWHW[ R`V[[ RWIRN R_GZL RYCVK R`C]J RbQ_Y R[OkOkUjZ RbJaU',
	    'PoZNkN RWIW[ R^D^Z RfTkZ RiEbM RVJRN RcNgW RYCUL ReX]Z',
	    'Po^[i[ RWHW[ R^M^Z RbDiKlL RjWi[ RVJRN RbCaFZM RYCTM RhO]T',
	    'PobTgT R]ZjZ RWHW[ R^M^Z RjVjZ RbDjL RVJRN RbDZM RYCUL R]MhMgT',
	    'Poe[j[ RWIW[ RVJRN R^LfP RYCUK R_C\\L RhS\\V R^HlHlSj[',
	    'PoZIlI RWIW[ RVJRN RbDcH R]JaSfXl[ RYCUL RiJeS_XY[',
	    'Pod[j[ RWHW[ RdPhT R\\E_H R_UZ[ RWIRN RYCVJ RbO]X RZKlKlSj[ RcCcIaS',
	    'Po[YlY RXIX[ R\\H\\L RcDcH R\\HlHlL RWIRO RZDWJ',
	    'Po^NjN RWHW[ R^EjEkN RgUlZ RVJRN ReOiX RYCVK R^E]SZ[ R^M\\W',
	    'PoZHlH R\\QeQ RcYkY R[ZcZ RWIW[ RcDdG RiL[Z RVJRN R`L\\P RhTk[ RYCUL RbH`M RfYaY',
	    'PoWGW[ RhWl[ R[QaX R_UY[ RgUa[ RePjY RjNeX RXDSN R`P]X RdKhW RkIgU RdGfU RcFkFhS R]CYT RYIbI_U',
	    'Po\\QlQ R\\ZlZ RWIW[ R\\H\\[ R\\HlHl[ RdCbH RZDWJRO',
	    'Po[JlJ R]ZjZ RWHW[ R]Q][ RcCcQ R]QjQj[ RVJRN RYCVK',
	    'Po^[b[ RWHW[ RbLb[ RbCkM RVJRN RaEYM RhPkW RYCUK R]PYW',
	    'Po\\KkK R`[k[ RWHW[ RaNa[ R\\EkEkK RlWk[ RVJRN RXCTM RjO`T R\\E[RZY',
	    'PoYKlK RbShS R\\ZlZ RWHW[ R\\D\\[ RbCbU RhDhU RXDTN',
	    'Po[LkL RYSlS RWHW[ RcCc[ R\\E^J RiEgJ RYDWIRN',
	    'PofQlQ R\\YlY RWHW[ R\\E\\[ RgEgQ R\\ElEl[ RWJRN RYCVJ RbEbK`Q',
	    'Po]KhK ReXiX RWHW[ RaPa[ RZPjPjX RbCiJlK RbC[K RYDWIRN',
	    'Po\\QlQ R\\YlY RXHX[ R\\J\\[ RdCdY R\\JlJl[ RWIRN RZCWJ',
	    'Po[MlM R[TlT RWHW[ R[H[U RcCc[ R[HlHlU RYDWIRN',
	    'Po]LiL R]SiS RYZlZ RWHW[ R]E]Z R]EiEiZ RVJRN RYCVK',
	    'Po[IiI R]UfU Rf[l[ RWHW[ R]N]W R]NfNfU R\\ElEl[ RZDWJRN',
	    'Po\\ElE RZQlQ RWHW[ RcEc[ RVJRN RhIjN RYCUK R_H\\N',
	    'PoWIW[ R\\E\\V RgUlY RbHdM RcS[V RYDVJRN RjEjPgUcY][',
	    'PoeXlX R]ZaZ RWHW[ ReGeZ ReGlGlZ RXCSN R^C^N\\X RZIcIbSaZ',
	    'Po[EkE R[ZlZ RWHW[ RcHcX RfLkQ RVJRN R]OYP RdH\\P RYCVK RdEcJ',
	    'Po]RjR RWHW[ R]F]R R]FjFjR RgUkY R_UZZ RVJRN RYCUL',
	    'Po^LkL R^ZlZ RXIX[ R^E^[ R^EkEkL R^RlRl[ RXIRO RZCWJ',
	    'Po_MkM R[NgN R\\ZjZ RWIW[ R\\Q\\[ R\\QjQj[ RfHlM RVJRN R_I[M RYCUL RbC_I',
	    'Po[FlF RcOkO RZZlZ RWHW[ R]L]Z RcFcZ RVJRN RYCUK',
	    'Po\\OlO R\\ZlZ RWHW[ R\\E\\[ RdEdY R\\ElEl[ RZDWJRN',
	    'PoZIlI R^QiQ R^YiY RWIW[ R^D^[ RiDi[ RWJRO RYDVL',
	    'Po[IkI RZOlO RWIW[ RbCb[ RdQlY RdOeS RaP`SZY RWIRN RYCVJ RbM`R',
	    'Po\\MiM R\\UiU RZZlZ RXIX[ R\\F\\U R\\FiFiU RYDVJRN',
	    'PoZNlN R^[c[ RWHW[ R[G[K RcCcG RcNc[ R[GlGlK RYCWJRN',
	    'PoYHlH RgWjW RWHW[ R]N]X RdJd[ R\\NjNkW RVJRN R^LYQ RYCVK RaC]M',
	    'Po\\HkH RZOlO RcYkY RaYhY R^ZeZ R[ZcZ RWHW[ RcCcO RWIRN RgTiVk[ RYCVK RaO^Z',
	    'PoWIW[ RdCdN RWIRN R_NbTfYl[ R]GlGkL RYDVK R]NjNhScX][ R]G]R[Z',
	    'PofNkN R]ZkZ RWHW[ R]Q][ R]QkQk[ RWIRN RYCVJ RcFaL^N[O R\\ElEkN',
	    'Po_LcL RfZlZ RWHW[ R[J[Z R_C_Y RfCfZ RlWlZ RXDTM RkJhLeN RcXXZ',
	    'PoZIkI RYZlZ RWHW[ RcCcI RhLdZ R^L_W RYDVJRN',
	    'Po\\MlM R[ZfZ RWIW[ R\\F\\W RdU[W RiD\\F RYDVJRN RdFdKgWjZlZmV',
	    'Po[IlI R\\QkQ RZZlZ RWHW[ RcIcZ R_DdE RYDWJRN',
	    'PoYIlI R_PkP R\\ZlZ RWHW[ RePeZ RYDVJRN RaE^O[W',
	    'PoZIlI R^YkY RWHW[ R^P^[ R^PjPj[ RYDWJRN RbD`L^QZU',
	    'PoYPlP RWHW[ R\\H\\P R\\HjHjP RVJRN RcCbPeViYl[ RYCVK RaU]YY[ RbLaU',
	    'PoYIlI R\\VhV RWHW[ RbCb[ RXDVJRN RbJ^QXV RcJgQlV',
	    'PocIlI R\\YjY RWHW[ R\\P\\[ RcCcP R\\PjPj[ RWIRN RYCVJ',
	    'Po\\TdT RWHW[ R\\L\\V R\\LdLdT RXDVIRN R[GlG RjGj[d[',
	    'Po_[g[ RWHW[ R_I_[ RcPYZ RVJRN RhMlT R_DdF RYCVK RiEdN RhVh[ R[MYU',
	    'Po^[k[ RWHW[ R[G[K R_L_Z RcCcG RlVk[ R[GlGlK RWIRN RYCVJ RiN_R',
	    'PoYLeL RTQjQ R[[_[ R_Q_[ ReSjY RYTTY R_CfJlM R_CWKRM',
	    'PoWLfL RSQkQ RZ[_[ R_L_[ ReTkZ RXTSZ R_CdHlL R^CYIRL',
	    'Po]IjI RZPlP RWHW[ ReUj[ RVJRN RcCcPgX RYCUK RcP`V]YY[ R]D[M RcKbT',
	    'Po[MkM RhXkX RWHW[ RfCf[ RZGkGkM R[MZSlRkX RYCVJRN R`D`Q^WZ[',
	    'Po^IlI RbNlN RbTlT RWHW[ RbHb[ RYDVJRN R`D]KZN',
	    'Po]UeU Rd[j[ RWHW[ R]M]V R]MeMeU RVJRN R\\JYM RYCUL R_C[L R]HlHlRj[',
	    'Po\\EjE RZJlJ RYPlP RWHW[ RVJRN RgSbWk[ RhPfV RYCVK RaK\\ViY ReW`YY[',
	    'PoWHW[ RVJRN R_GZL R_IfPlS R`RfU RYCUK R`C^I R_GhGgJaPYS RiX]Z',
	    'Po`[d[ RWHW[ RdId[ R]IlIkM RVJRN RhOlW RYCUK R_O[W R_D\\M',
	    'Po\\YjY RWHW[ R\\O\\Y RcLcY RjPj[ RcDjL RVJRN RcE[L RYCUL',
	    'Po^ElE R^SjS R^ZlZ RWHW[ R^E^Z R^KjKjS RVJRN RYCVK',
	    'Po\\LlL R\\RlR Rh[l[ RWHW[ RdEd[ R\\ElEl[ RWJRN RYCVJ R\\E\\R[Y',
	    'Po[EkE Ri[l[ RWHW[ RVJRN RaOdT RlWl[ RYCVK R_E^KhKhTiZ R^K^P]S]U\\Z RaZZZ RfY_Z',
	    'PoXMeM RS[k[ RVQYW R_DeJlM RhPc[ R_DYKRM R^P`W',
	    'Po[ElE R^ZkZ RWIW[ R_M_[ R]OkOk[ RVJRN R^OYS RYCVK RaF\\Q',
	    'PoaGkG RYNlN RWHW[ RaCa[ RVJRN RcRjV RYCVK',
	    'Po\\RfR R]ZkZ RWJW[ R]K]Z R\\KfKfR RlWkZ RVJRO RYCUM R^CZL R]GjGjTfT',
	    'Po]IgI RdWfW RVGV[ R^N^X RbIb[ R^NfNfW RlVl[ RXDRO RZEjEjVj[ RZEZRY[',
	    'PoaTgT R\\YlY RWHW[ R\\E\\[ RaJaT RaJgJgT R\\ElEl[ RWIRN RYCVJ',
	    'Po^GhG R]ZiZ RWIW[ R]Q][ R]RiRi[ RVJRN R_FZK R_IdNlR RYDUM R_C]J RgHaOZR',
	    'PoZGgG RYMlM R_[j[ RWHW[ R_Q_Z RaCaM RkWj[ RVJRN RXCTM RjFaO_QXT RiR^U',
	    'Po\\QjQ R\\WkW RWHW[ RdNd[ RVJRN RaC[J R_GcKlO RYCVK R\\S\\W R_GiFcLZP',
	    'Po]MhM R\\ZiZ RWHW[ R\\R\\[ R\\RiRi[ RcElM RVJRN RbC`GYM RYCUK',
	    'Po[IlI R[NkN RZTlT RWHW[ RcIc[ R]C_G RhCgG RWIRN RYCVK',
	    'Po[ElE R]RkR R]YkY RWIW[ R]K][ R]KkKk[ RWJRO RYCUL RcEbJ',
	    'PoYLeL RTPjP R_UiU RR[l[ RXSX[ R_P_[ R_CdIlL R_CXJRM',
	    'Po`ZlZ RXHX[ R`Q`[ R^RlRl[ RiIaQ RWIRN RbD[K R`GjGgK RYCVJ R`KcOZS',
	    'Po\\GjG RZMkM R[SjS RYZlZ RWHW[ RcCcM RcOcZ RXDVJRN',
	    'Po[ElE R^KiK R^PiP RWHW[ R^E^W RiEi[ RVJRN RXCTL RjUcV\\W RhUaV',
	    'Po[IkI RZQlQ RWHW[ RgIg[ R^C_H RhCfH RYCWIRN R_I_S^W[[',
	    'PoZHlH R[NkN R\\ZjZ RWIW[ R\\R\\[ RcCcN R\\RjRj[ RVJRN RYCUL',
	    'Po]JjJ R]OjO RWHW[ R]E]Z R]EjEjO RdTjZ RWIRN RbOeW RYCVK RkReU ReYZZ',
	    'PoZKlK R\\YjY RWHW[ R\\R\\[ RdEdR R\\RjRj[ RWIRN RYCVJ RiD]F',
	    'Po\\KlK RfUiU Ra[k[ RWHW[ RbNbZ RaNiNiU RlWl[ RVJRN R]IZL RYCVK R_FfFdJ R_C\\J]S[Z',
	    'Po]EdE R]NiN R\\TjT R[[l[ RWHW[ RcNcZ RdFkM RVJRN RbE[L RYCUK',
	    'Poe[l[ RWIW[ ReDe[ RlXl[ R[H]L RjGfK R^VY[ RVJRN RePjR RYCUL R_QYS R`D_Q^W',
	    'PoZGlG RWHW[ RcCcG RfJkO R_JZO RWJRN R_O`TeXl[ RYCUL RgNdTaXY[',
	    'PoYI^I RWHW[ RgVl[ RcU^[ ReHeQhY RePaX RaIlHjO RXDTN R_RYU RbC_N ReLdU',
	    'Po]PhP R]UhU Rd[h[ RWHW[ R]KhKh[ RhHlL RVJRN R]IYL R`DgDjK RYCUK R^F[K R]K]T\\Y',
	    'PoZGlG R[QkQ RWHW[ R[K[Q R[KkKkQ RYDVJRN RcDcRaV_YZ[ R\\TcYk[',
	    'PoYNdN Rh[l[ RWHW[ RZEcEb[ RgFgT RlDl[ RXDSN R\\E\\S[Y',
	    'Po\\IjI RYOlO Re[l[ RWHW[ RcCcO RfOf[ RlWl[ R^WZ[ RVJRN RYCUK R^DZL R_O^W',
	    'Po[GlG Rc[k[ RWHW[ RdKd[ RcCcF RlWk[ RXDTM RiI]L R[G[RZZ RfQ]R RkQcR',
	    'PoWHW[ RbPbZ R]Q\\Y RYDWIRN RiPi[l[mW R\\EjEjM\\M\\E',
	    'Po[ElE R^MkM R[MgM R\\SjS RZZlZ RWHW[ RcNcZ RfHlN RWJRN RYCVK RaE^M',
	    'PoZHlH RWHW[ RfUl[ RgKaQ RVJRN RcCcH]M RYCVK R[M^NaQZT RjPdVZ[',
	    'PoSHkH R_C_[ RWM[P RfMjQ R_OfWlY RfJcP R_OXWRY RXJVOSR',
	    'PoZGkG R\\NjN RWIW[ R[JjJjN RVJRN RcCcRdVgYl[ RYCUL RaU]YZ[ R\\N[RkRjVgV RbMaU',
	    'PobPlP RWHW[ RVJRN RYCVK RbC^HaIdK\\P R`FiFdMaQ[T RkRfW_Z R`GZI R_RbTeWZ[',
	    'PoZGlG R^MgM R[QiQ Rb[f[ RWHW[ ReIlO RVJRN R_IYO RYCVK RaC]K R_Q]UhTf[',
	    'PoYNlN Re[k[ RWHW[ RcCcN ReNeZ R]WY[ RVJRN R[E^K RjEgL RYCUK RlVk[ R_N^W',
	    'PoZEeE Rg[l[ RWHW[ RfFfU RlDl[ RXDULRN R^E\\LZR R[OaS R]KdKaS^W[[',
	    'PoZIdI RVQhQ RSUkU RTZhZ ReWi[ RcVfW R_CbFlK R[UXZ RVLfLdP R^C[GRK',
	    'Po\\GjG RZMlM RZQlQ Rb[g[ RWHW[ RcCcL RgMg[ R]TaW RYDVJRN',
	    'PoYNlN RWHW[ RcCc[ RfRlX R_SYY RVJRN R[E^K RdNfS RYCUK RaN_T RbL`Q RiEgK',
	    'Po\\IjI RYOkO RWHW[ RbCb[ RdRlY RdOeT R`RYY RVJRN RXCTM R]DZL RaO_T',
	    'PoYZlZ RWHW[ R[H[Z R`H`Z ReHeZ R[HjHjZ RVJRN RYCVK RbC`J',
	    'Po[GlG R^PjP R^UjU Rf[j[ RWHW[ R_J_[ R_LjLj[ RYDWJRN RbD_KZP',
	    'PoZOlO RWHW[ R[G[K RcCcG R[GlGlK RVJRN R^Uk[ RYCVK RaJ_O\\V RhOeUcX_ZZ[',
	    'PoZJlJ R[OjO R[SjS RYWlW RWHW[ RbCb[ R[FjFjN RVJRN RYCVK',
	    'PoZIjI R\\OkO RYTlT RWHW[ RcKc[ RgFkK RWJRN RYCVK RaC_H R]LZR',
	    'PoZElE RVIV[ R[K[[ RaHa[ RfKf[ RVJRN RXDTM R[KkKk[h[ RbFaN',
	    'PoUUiU Re[i[ RUOU[ R\\O\\[ RbOb[ RUOiOi[ R_CeI R^CXKeJlL RXJRM',
	    'Po_IiI R`UhU Rg[l[ RWGW[ R[D[[ R`M`V R`MhMhT R[DlDl[ RUKRN RXDTM',
	    'Pob[k[ RWHW[ RZGZK RcLc[ RcCcG RlWk[ RZGlGlK RiK[M RdRZS RgR]S RjQ`S RYDVJRN',
	    'PoZIlI RZOdO RWHW[ R_O_X RhDkG RVJRN RYCUK RfCfNhY RdWZX RlWk[i[',
	    'Po]QfQ R]VfV Rd[j[ RWHW[ R]L]X R]LfLfV RWJRN RYCVK R_C[L R]HlHlSj[',
	    'Po[JlJ RZSlS RWHW[ R_D_R RgDgR RfVl[ R_VZ[ RYDVJRN',
	    'PoYOlO Re[h[ RWHW[ RaC`G RbQbW R]GiGi[ RaJdM RVJRM RXDUK R]G]T\\Z',
	    'PoYIkI RWHW[ R^N^Z RcDcIaKZR RkMhPeQ ReX\\Z RYCVJRN RcIeRgWl[',
	    'PoeYlY RWHW[ ReFe[ ReFlFlZ R[QbX R_UZ[ RUKRN RXDTM RaP]X R^CZT RYIbI`U',
	    'PoYHlH R`RlR Rb[f[ RWHW[ R^K^[ RfOf[ RbLkLfP RVJRN R]LYQ RYCVK RaD\\N',
	    'PoZHkH RYQlQ RWHW[ RVJRN R\\J^O RcCbQeVhYl[ RhJfO RXCTL RaU]YX[ RbLaU',
	    'PoZFlF R[YlY RWHW[ R[M[[ R`F`Y RfFfY R[MlMl[ RYDWIRN',
	    'Po[EkE R\\NjN RYSlS Rf[j[ RWHW[ R\\I\\[ RbEbS R\\IjIj[ RXDSN',
	    'Po^LkL R\\ZlZ RWHW[ R\\Q\\[ R^D^L R^DkDkL R\\QlQl[ RWJRN RYCUL',
	    'Po]GhG R[LlL R`PkP Rc[h[ RWHW[ RbCbL RVJRN RYCVK RjFaOYS R_UiUh[ RbN`U',
	    'PodGjG R`GgG R^GdG RYRkR Rd[l[ RWHW[ ReReZ RlXl[ RkLjO RVJRN RYH`FbKeNiO RYCUL R_CaK RhIbL[N R_R^X[ZX[',
	    'PocFkF RXHX[ R]K]U RcCcK R]KjKjU RWIRN ReWkZ RYCVK RcNcU`YZ[',
	    'Poh[l[ RWHW[ R[E[U R[EcEcU RhFhU RlCl[ R`WdZ R^WZ[ RXCSN R_H_R^W',
	    'PoZKkK RWHW[ RfQf[ RVJRN RaK[Q RdKiPlQ RYCVK R[FbE_N R^Q]X RhD_E',
	    'Po]MgM RZQjQ RcYiY R^ZdZ R[ZbZ RWHW[ ReUiX R^T]WZZ RVJRN RbDZL RbDhJlL RXCTL R`Q^W',
	    'PoZGlG RWGW[ RgQg[ R^WZ[ RbCcG RXCTM R^HbMfNlP RgIcM`NYP R^Q^X',
	    'Po[GkG R[MkM RYQlQ RWHW[ R[G[L RcDcG RVJRN RYCVK RaN]Vk[ RhQgUcX[[',
	    'PoWHW[ RZHZK R^O^Z RZHkHkK RfUlZ R_NYT RhQeR RjNgQ RdNhW RXDTN RcF^O RcIfU RcD`M RdY]Z RcGeS',
	    'Po\\KjK RWHW[ R\\EjEjK RaQdS R_PcQ RfPlV RUKRN RdKgR R^VdY RXCTL R\\E\\L[S',
	    'Po\\HkH RYQlQ R[XkX RWHW[ R^CZL R^L[X RYDWIRN R^LjLjTg\\c\\ RdLdQbX',
	    'PoZJlJ R\\OjO RYTlT RWHW[ R]EhEhJ R]K[R RYDWIRN RcOcU_YZ[ RcUgYl[',
	    'PoYJlJ R[NiN R^ZjZ RWHW[ R_Q_[ R\\EiEiN R]SjSj[ R_QYW RVJRN RYDUL RaI^T RaF`P',
	    'Po\\KjK R[OkO R[TkT RYZlZ RWHW[ R\\D\\K RcOcZ R\\DjDjK RVJRN RYCVK',
	    'Po\\EkE R^IjI R[NkN RWHW[ R_N_Z RfVk[ RdQhY RXDSN RjReT RdNfW RfY\\Z R\\E[QZY',
	    'Po[SjS RYZlZ RWHW[ RbDbZ R\\J`N RgJlO RVJRN RXDTM R]HYP RhFeO',
	    'PoYJeJ RTN_N RUT_T RW[`[ RS[\\[ Re[i[ RdOdW RiNi[ R_DbGlK R_CZHRL RYNW[ R_P^Z',
	    'Po]HjH R\\LjL RWHW[ RZOZR R]EjEjL RZOkOkR R^SbWfYl[ R\\SiSeW`YZ[ RYCWIRN',
	    'Po]MkM R\\ZlZ RWHW[ R\\R\\[ R]E]L R]EkEkL R\\RlRl[ RcMbR RZDWIRN',
	    'Po\\JkJ R`WhW Re[k[ RWHW[ R`R`Y R`RhRhW R\\DkDkJ RVJRN RYCVK R\\NlNlVk[ R\\D\\Q[Y',
	    'Po\\DlD R]PjP R[[l[ RWHW[ R]I]P R]IjIjP RVJRN R_SaY RhSeZ RYCUK',
	    'Po`MlM RaVkV Ra[l[ RWGW[ RfEfV R\\WX[ RXDSN R^RZY RZE^EZN_N\\X RjDaF RZQ\\W_Zg[',
	    'Po[HjH RWHW[ R[D[H RjDjH RaOcQ RVJRN RcCcJeMlP R[SiSfX RXCTM RbI^MYP',
	    'Po]QiQ Re[k[ RWHW[ R]I]Q ReQe[ R\\JjJjQ RlXk[ RhHlK R_W[[ RVJRN R]HYL R`DgDiJ RYCUK R^F[J R`Q^X',
	    'Po[GlG R^OhO RZTlT R_[c[ RWHW[ R[K[N RcCcK RcRc[ R[KlKlN RVJRN RYCUK RgPcR',
	    'Po\\JlJ R\\OlO Rf[j[ RWHW[ R\\E\\O R\\ElElO RWJRN RYCVK RbV_Y[[ R[SlSk[ RdEdNbV',
	    'Po\\GgG RZLlL R^PiP R\\UlU R_[d[ RWHW[ RbCbK RdSd[ RVJRN RYCUK RjFaPYT RhQdS',
	    'PoZElE R[MjM R[RjR RWHW[ R[I[R R[IjIjR R[T^WbYl[ RXDVIRN RcEcQbU_YY[',
	    'PoZPlP RWHW[ RcGcK RhFlJ RVJRN R]Uk[ RYCUK R]EZL RaL\\V RhPfVcX`ZZ[ RiD]E',
	    'Po`KiK RYTlT RWHW[ R`D`K R[F[OiNiS R`DiDiK RVJRN RcUgYl[ RYCUK RaV^YY[ RbNaW',
	    'Po]RkR RWHW[ RcRc[ RgUkY RiOmS RgH]R R]UYY RiD^F RYDWIRN RaF]KaN',
	    'Po\\LjL RdSkS RWGW[ R\\E\\L RdLdZ R\\EjEjL RXCTN R^Q\\WZ[ R\\VaZi[m[',
	    'PoYLlL RZ[^[ RWHW[ R^F^[ RiEkI RXDTN RcQYT RbDZF RfDfNgZk\\lW RjOhTbZ',
	    'PoYHlH R^[b[ RWHW[ RbCb[ R[L^O RfSkY RjLgP RVJRN RbKgU RfDjF RYCVK R_UYW RbHeS',
	    'Po]LhL RZQkQ R^[b[ RWHW[ RcLc[ RgTkX RcCjK R]UYY RVJRN RbDZL RYCUK',
	    'PoaElE RbOkO R`ZlZ RWHW[ R_K_Y RfEfZ R_FYL R]PYT RXDSN R[D^K[R R^[[[ R^G_Q',
	    'Po]HkH RZNlN R]ZjZ RWHW[ R]R][ RcCcQ R]RjRj[ RVJRN RYCUK R^D[K',
	    'Po[EkE RZOlO R\\ZjZ RWHW[ R\\S\\[ R\\IhIhO R\\SjSj[ RWJRN RYCVJ RaE_O',
	    'PoZIjI RWHW[ RgElJ RaO[U R^ScWj[ R`C^I R`I^NZO ReIeNjNkM R_RhRdW`YY[ RYDWIRN',
	    'Po\\JiJ R[SjS R\\[k[ RWHW[ R\\M\\Z R\\D\\J RcMcS R\\DiDiJ R[MjMjT RlWk[ RVJRN RYCVK',
	    'Po[HlH R\\QkQ R\\UkU Rg[k[ RWHW[ R\\L\\[ RcCc[ R\\LkLk[ RVJRN RfDjE RYCUK',
	    'Po\\KiK R\\PiP RWHW[ R\\F\\Z RcCcG R\\FiFiP RVJRN RbPdVhYl[ RYCVK RjTeV RdYYZ',
	    'Po`LiL R`TiT R\\[l[ R`E`[ R`EiEi[ RWH\\M RWS\\X RVKRN RVVRZ RYDUM RXPTY',
	    'Po]QjQ R]UjU Rf[j[ RWHW[ R]L][ RcCcL R]LjLj[ RgElJ RVJRN R^G[K RYCUK',
	    'PoYLdL Rh[l[ RWHW[ R_F_[ RgFgU RlDl[ R`McR RXCSN R_MZV RcEYF',
	    'Po\\ElE R\\PlP R\\UlU RWHW[ R\\K\\[ RdKd[ R`GiK RYDTM RkEdH R\\KlKl[h[',
	    'Po]KiK RZPlP Re[l[ RWHW[ RZGZJ RcCcG RfPfZ RZGkGkJ RlWk[ R^WZ[ RVJRN RYCUK R_P^X',
	    'Po[ElE R\\SkS RZ[l[ RWHW[ RcSc[ R[L^O RaLeP RfLkP RjHfK RVJRN R^H[K RdHaK RYCVK',
	    'Po\\IjI R\\NjN R\\SjS Re[l[ RWHW[ R\\E\\S RfSf[ R\\EjEjS RlWl[ RWIRN RYCVK R`S^X]ZY[',
	    'PoZNkN RWIW[ RZGZK RbIb[ RZGkGkK RdQlY RbCcG R`RYY RVJRN RbMeS RYCUL RbM_T',
	    'Po[FlF R]ZjZ RWHW[ R]R][ RdHdP R]RjRj[ RWJRN RcIjL RYDUL ReFaJZN',
	    'Po\\ZiZ RWHW[ R\\R\\[ R\\RiRi[ RbHiOlP RfDkI R_DZI RbIZQ RYCWIRN',
	    'PoZTlT R_[c[ RWHW[ RbGbK RcQc[ RgFlK RVJRN RYCVK R\\EYL R\\MjMcQ RiD^E',
	    'PoZMlM RgYkY RWHW[ R^P^Y RdNd[ R\\RkRkY R`NYT RVJRN R_EiJ RYCUL RaJ_O R]EgH RhD`H[J',
	    'Po[JjJ R[PjP R[UkU RYZlZ RWHW[ R[E[P RcEcZ R[EjEkP RVJRN RYCTL',
	    'Po\\RkR Rd[l[ RWHW[ R\\I\\R RdRdZ R[KkKkR RlWl[ RVJRN R^GYL RYCUK R_C]H RaU]YY[ R^GfFdJcKbU',
	    'Po\\JjJ R\\NjN RZQlQ Rc[h[ RWHW[ R\\F\\N RcCcN R\\FjFjN RVJRN RYCVK R_Q]UiUi[',
	    'Po\\LjL RZQkQ RWHW[ R\\E\\L RcLc[ R\\EjEjL RdSkY RcRaS[Y RYDWIRN',
	    'PoYJeJ R[[][ Re[h[ RVNV[ RVN]N][ RbObW RhNh[ RXR\\R RWV[V R_CdHlK R^CYHRL',
	    'PoZJjJ R\\OjO RYTlT RWHW[ RfFkK RVJRN RcNcUfYl[ RYCUK R`C]I R]K[R RbU_YZ[',
	    'PoZHlH RWHW[ R]M_Q RhNlR RhLeR R]KZR RYCVJRN RcDcRaV^YZ[ RcReWhYl[',
	    'Po\\EiE RZHlH R\\LjL R\\PjP R\\ZjZ RWHW[ R\\T\\[ R\\TjTj[ RYDWIRN',
	    'Po^GkG RWHW[ RVJRN R_FZJ RYCUK RiFcL[P RdP\\S R_C]HbLfNlO RgSbU\\V RjVeY][ RfY[[',
	    'Po[OkO RYTlT RWIW[ RYDVJRN RbObT^YY[ RbUfYl[ R[EiEiK[K[E',
	    'Po[NkN RgYkY RWHW[ RcHc[ R[HkHkM R^C_G RhCgG R[MZSlRkY RYDWIRN RcS^XZZ',
	    'PoZGlG R[NjN R[SjS RZVlV RWHW[ R[J[S RcCc[ R[JjJjS RVJRN RYCVK',
	    'Po[GkG R[JjJ RZNlN RZRlR Rd[h[ RWHW[ RhNh[ R`UcX R^TZY RWIRN RYCVK RaN\\W RbD`R',
	    'PoZHlH R[NkN R^ZfZ RWHW[ R_R_Z RbCbN RaPdS RgVfZ RiSkY RXDTM R[SZY',
	    'Po\\ElE R[NlN RWIW[ RaEaN RfEfN RVJRN R\\G^K RjGhK RYCUL R[N[UZZ',
	    'PoZElE Rg[k[ RWGW[ R[K[[ R[KkKk[ R`E`ObS RfEfOiU RXDSO R`J_T RfJdT',
	    'PoZElE R^[`[ Rh[k[ RWGW[ RZJZ[ RZJaJaZ RdJd[ RdJkJk[ R\\O^T RfPhT RXDRO',
	    'Po[GkG RZPlP RWHW[ RcCc[ RbPkX RcPZX RVJRN R]I^M RhIfM RYCVK',
	    'PoYKbK RVGV[ R_E_[ R^LaP RXCSN R^JZT RiPk[ RbDYF RfDcY RkXaY',
	    'Po]LgL RYYlY RWHW[ RbDiKlL RaEZL RiOdY RXCTM R\\P]V RbOcU',
	    'Po_GkG RWHW[ RZFZW R_GdLkO RXDSN RiGdL\\P ReP^S RgS_W RjVeY][ RaD_H]K',
	    'Po[GlG RbMlM Re[i[ RWHW[ R`K`[ RcCcG RiIi[ RcQeU RXDTN RaI]P R[GZSYZ',
	    'PoYLdL ReXlX RWHW[ R_E_[ ReFe[ ReFlFlZ R^McS RXCTM R^KZV RcDYF',
	    'Po]HiH R]LiL R]PiP RZUlU RWHW[ R]D]T R]DiDiT R_W\\ZY[ RYDWIRN RfWiYk[',
	    'PoYGcG RWHW[ R^C^G R[PbW R]VY[ RaJcN RXDTM R[JYO R`O\\Y Rk[g[ RcJlJlUk[ RgDgMeW',
	    'PoZH`H ReHlH RZN`N ReNlN ReTlT RWHW[ ReCe[ RYDWIRN R`D`Q_V[[ RZT_T',
	    'PodGkG RaHhH R_HfH R`SiS RWHW[ R`DcK R`QbW RVJRN RZHbIeMjO RdOhQ R[TbTeYi[ ReCiE RkMkO RYDUL RgJaM[O RhUbYZ[ RlYl[i[',
	    'PoZFkF R[JjJ RYNlN RWHW[ R^R^Z RjReT ReYZZ RYDWIRN RcDcO_RYT R RcOeVl[',
	    'PodFkF R^GbG RYMcM R[[^[ RWHW[ R^C^[ RgVb[ RbQcV RfQkZ RjOeX RXCSN ReKiX RkJgV RZQXW ReHhV RkGiT',
	    'PocOkO Rc[k[ R\\[_[ RWGW[ R[E`E`[ RcEc[ RgEgO RcEkEkQ R\\J^L R_Q\\R RlWk[ RXDSN R[EZTY[',
	    'PoZFkF R[JjJ RYNlN R]RhR R[VjV RWHW[ RcOc[ RYDVJRN RbDaJ_NZS RcJfNmS',
	    'PoZFlF R[K`K ReSkS RZZlZ RWHW[ R`F`K R[K[S`S`Z ReSeZ ReFeKkKkS RVJRM RXDUK',
	    'PoYGkG R^RjR R]VjV RWHW[ R]N]W R]NjNjV RdIlO RYDVJRN RcDbG^KYO RcJc[k[lX',
	    'Po]UiU R]ZiZ RWHW[ R]Q][ RcQcZ R]QiQi[ RVJRM R_E[J R^GcMkP RXDUK R^GhGcLZQ',
	    'Po[FlF R[LjL R]SiS R]WiW Re[i[ RWHW[ R]O][ RcCcG R]OiOi[ RgHlM RVJRN RYCUK RaF^L',
	    'PoZLbL RVHV[ R_L_[ RgIgSjZ RfScZ RXDRO RfOiX RdIkIjO RdCbO RfMhV R`DZFZRXZ RfLeV',
	    'Po[KkK R[PkP RYUlU RWHW[ R[F[P ReQe[ R[FkFkP RVJRN RYCVK RcFcP`R\\T',
	    'PocFkF RbQkQ Rf[l[ RWHW[ RbKbP RcCcI RfSfZ RlWl[ RVJRN R[IlIkL RYCVK R^Y[[ R`S_Y R[I[SZZ RdM^M RhLaM',
	    'Po^EjE R^IiI R^LiL RZQlQ RWHW[ R^E^Z RVJRN RbQeW RiTdUhYl[ RYCVK RdYZZ',
	    'Po[HcH RZOcO ReYlY RWHW[ ReGe[ ReGlGlZ R_H^ScX RXDTM R\\CZL R_M]W',
	    'PoZJbJ RcJlJ RYRbR RcRlR RWHW[ RgJg[ R]WY[ RVJRN RYCVK R_C^H RhCfH R^J^R]W',
	    'PocPlP R\\ZiZ RWHW[ R\\S\\[ R\\SiSi[ RfHkM R`KZQ RXDSN R]CZJ R\\FbF^N RgDdL R\\G^LaOhP',
	    'PoZLlL R_SjS R_WjW Rf[j[ RWHW[ R_O_[ R^QjPj[ R_OYU RVJRN RgGkI RYCVK RbI^Q R]DiH RjDcG[I',
	    'PoZGkG RYTlT RWHW[ RbCbG RbRb[ R^LaO RfMhNkP RfLbP RVJRN R^LYP RYCUK R_I\\N RgIdO',
	    'PoZElE Rg[k[ RWHW[ R[J[[ RcEc[ R[JkJl[ RXDTM R_N]V R_N`U ReNgNeV RgNhU R_N]N',
	    'PogUjU Rd[l[ RWHW[ RZGZK RcCcG ReLe[ RdLjLjU RZGlGlK RlWk[ R\\Q_U R_VZ[ RXDTM R]JYS RaP]X R\\MbM_V',
	    'PoYHkH RVIV[ RbCb[ R]L`P RbPiWlX RgLkQ RbPZX RUJRN RhJeP RXDTM R]JZP',
	    'PoWOgO RWSgS RYZhZ RYUY[ RWLgLgR RYUhUh[ R`CcG R^CZHdHlK RZHRK RWLWRVX',
	    'Po`EfE R`OiO R[PdP R_[g[ RWHW[ R`T`[ RgXg[ RjVlZ RfLjP RgFkJ RVJRN R^GZK R`ReT RYCUK RaI^O R\\UYZ',
	    'Po^KjK RWGW[ RdGdO RXDVJRN R[EmEm[ R[E[[ R[ZmZ R`OhOhV`V`O',
	    'Po]PiP R]ZkZ RWHW[ R[F[J R]J][ RcCcF R]JiJjP R]TkTk[ R[FlFlJ RVJRN RYCVK',
	    'Po[GlG RZNlN R]ZjZ RWHW[ R]R][ RcCcG R]RjRj[ RVJRN R^H_L RhHgM RYCVK',
	    'Po[NjN RYUlU RWHW[ R[H[T R`C`T ReCeT R[HjHjT RgWk[ R^VZ[ RXDTM',
	    'Po]SkS RWHW[ RZFZV RgPiR RcC]J R`GeLlO RdRfWhZl[ RXDTN RaGiGeL]O RcV`Y\\[ RdPbW',
	    'PoWHW[ RZGZV RjRgU RbE]J R`GdLkO ReVhYl[ RXDTM R_R]V R`GiGdL\\O RbV`Y[[ RdOeW RcObW',
	    'Po[IbI ReIlI R[NbN ReNlN Rf[l[ RWHW[ R[E[[ R[EbEbM ReEeM ReElEl[ RXDTM',
	    'PoZEeE R[LdL RZRdR Rh[l[ RWGW[ R_N_X RgGgT RlDl[ RUKRN RbIdM RXDTM R^E[L ReXYY',
	    'PoWHW[ RiEgI RVJRN RbJkN RcNcUfYl[ RiQhU RYCUK R]F\\I R]Q[U RbJ_M[O RbU_YZ[ RcCbK',
	    'Po\\JlJ R_RlR R^ZlZ RWGW[ R^T^[ R_M_R ReKeZ RlMlR R\\DlDlJ RlTl[ RXCTM R\\D\\QZY',
	    'PoZJkJ RYOlO RZTkT R[ZjZ RWGW[ R]J]T RZEcEcZ RhJhT RXDSN RiDaE',
	    'Po[GjG RYKlK RZQlQ R[VkV RWHW[ RcCcK RcQc[ RVJRN R^L^P RhLfP RYCVK',
	    'Po^[b[ Rh[l[ RWHW[ R\\DbDb[ RfDlDl[ R^P`S R^J`L RhPjS RhJjL RXCTM R\\D\\QZY RfDfTd[',
	    'Po`VfV Rf[j[ RWHW[ R\\K\\[ R`P`X RcCcK R`PfPfV R\\KkKk[ RgElJ RVJRN R^EZJ RYCVK',
	    'Po\\JkJ R_OkO R\\TlT RWHW[ R[H[V R`EhEhJ RdTeWhYl[ RXDTM RaL^R RdNdV`Y\\[',
	    'Po[FkF RYNlN R\\WdW Rd[i[ RWHW[ R\\R\\Y R\\RdRdW RiNi[ RVJRN RbHkL RYCUK RbH`K[M RcCbJ',
	    'Po[GkG R^LhL R^PhP RYTlT RWHW[ R^C^T RhChT RgVl[ R_VZ[ RVJRN RYCVK',
	    'Po_IiI R_MiM R_VhV Rg[l[ RWHW[ R_Q_X RdFdO R_QhQhV R\\ElEl[ RWJRN RYCVK R\\E\\SZZ',
	    'PoRG_G RSN^N RSR^R RRV_V RSJSR RYCYJ RYRY[ RSJ^J^R RhWl[ RaV][ ReQiY RcQ`X RdCdNhW RdKbU RdHfT',
	    'PoZGlG R\\RiR R^[b[ RWHW[ R\\K\\Q RcCcG RcRc[ R\\KiKiQ RgUlY R^UYZ RVJRN RYCVK',
	    'PoZGkG RYLlL R\\UiU R\\ZiZ RWHW[ R\\P\\[ R_C_L RfCfL R\\PiPi[ RVJRN RYCVK',
	    'Po\\IkI R]NjN R]SjS R]YlY RWIW[ R]G][ RdHdY RVJRN RcDeG RYCUL R^DZM',
	    'Po]IkI R]MkM R[UlU R[ZlZ RWIW[ R[Q[[ R]E]M R]EkEkM R[QlQl[ RWJRN RYCVK',
	    'PoZFlF RZMlM R[QjQ RcUiU Ra[l[ RWHW[ RcCcZ R[IjIjQ RVJRN RYCUK R\\TY[ R\\V_ZgZ',
	    'PoYHbH RWHW[ RXDSN ReCbN R_[\\[ R]D]H\\PZY R\\MaMaV_[ RdIlI RkJgUfWb[ RdJfRiWl[',
	    'Po[FlF R_NjN R_RjR R_VjV R[ZlZ RWHW[ R[K[[ R_J_V RdDcJ R_JjJjV RVJRN RYCUK',
	    'Po[SkS RZZlZ RWHW[ R[G[K RcCcF RcScZ R[GlGlJ RfGfNkMkN RVJRN RYCVK R^N[O RaG_M',
	    'Po\\IiI RYMlM RcWgW R^[i[ RWHW[ R^R^[ R^RgRgW RfMlS RgEkI RiXi[ RVJRN R]EZI R_MYT ReIhP RYCUK RbG]P RbC_M',
	    'Po]LiL R\\QkQ R`[c[ RWHW[ R[G[J RcCcG RcQc[ R[GkGkJ RhTkX R]VZY RVJRN RYCVK',
	    'Po\\JjJ R\\NlN R^ZkZ RWIW[ R^S^[ RdJdS R\\EjEjJ R^SkSk[ RVJRN RYCUL R\\E\\QZY',
	    'Po[FkF R\\IjI RZMlM R\\UiU Re[i[ RWHW[ R\\P\\[ RcCcM RcPcU R\\PjPj[ RWJRN RYCVK',
	    'Po\\KbK RdKjK R\\QjQ Re[l[ RWHW[ R\\F\\Q RfQf[ RdEjEkQ RlWl[ R^WZ[ RVJRN RYCVK RbD\\E R`Q^X',
	    'Po^KgK R[UjU Rg[j[ RWHW[ R[O[[ R`O`[ ReOe[ R[OjOj[ RcDiJlK RVJRN R\\JXK RbD[K RYCUK',
	    'PobFkF R\\NjN R\\RjR RYVlV RWHW[ R\\J\\R RbCbJ RcRc[ R\\JjJjR RVJRN RYCUK',
	    'PoZIlI RYRlR RWHW[ R[FcEcO RVJRN RcHfLlN RYCUK RcH_LYO R_Q]Vk[ RgTdXaYZ[ RiD`E',
	    'Po\\IjI R\\NjN RZRlR RWHW[ R\\D\\N RcDc[ R\\DjDjN RdSjY RVJRN RXCTM RcQ]XXY',
	    'PoXJaJ RVGV[ R]C][ R]J_Q RXCSN RiDkM R]HZS RdDbN RfMbZ RiTkZbZ',
	    'Po\\FjF RZJlJ RWHW[ RcCcJ ReJeOkNkO RaP[U RVJRN R_L\\P RYCUK R^ScXl[ R_ShSdW`YY[',
	    'Po[IjI R[MjM R\\SbS Rd[l[ RWIW[ R[D[M R\\P\\[ RePeZ R[DjDjM RlWk[ RVJRN RYCUL RkRdU RbZ[Z',
	    'PoZGkG R[NjN R[RjR RWHW[ R[J[R RcCc[ R[JjJjR R^XYY RcR\\Y RVJRN RcRiXlY RYCUK',
	    'PoZFlF R\\MjM RYSlS R[WjW R_[c[ RWHW[ R\\I\\L RcCc[ R\\IjIjL R[PjPjX RVJRN RYCVK',
	    'PoZGlG R^MhM RYTlT RWHW[ RcJcQ RgQg[ RfHkM R^WZ[ RVJRN RaGZN RYDUL RbC_J R_R^X',
	    'Po[FdF RYJlJ R`QeQ RWHW[ R\\N\\Y R`K`Y RhDkG RVJRN RlWk[ RYCUK RfCfLfNhY ReXYY',
	    'Po]IiI R]MiM R]QiQ RYUlU RWHW[ R]D]Q R]DiDiQ RiXl[ RfWjY RWIRN RYCUK R_W]YY[',
	    'PoYGkG R]NgN RZRkR R^[b[ RWHW[ RbRb[ RgUkY ReIkO R]VYY R`HYO RXDSN RbC^J',
	    'Po\\FbF R[QlQ RWHW[ RcNc[ RbHcL RcQjX RcQ[X RWJRN R]I^M RiGgL RYCVK RjD_F',
	    'Po]JgJ R[TjT RWHW[ R[N[T R[NjNjT RVJRN RbJbScVgYk[ RcChIlJ RYCUK RbC\\IXK RaU^YY[ RbNaV',
	    'Po[FjF R\\IiI RZMlM RWHW[ R\\P\\V RcCcL R\\PjPjV ReXl[ RXCTM RaYY[ RbRbX',
	    'Po[JaJ R[PaP RWGW[ R[E[V RYEbEa[ ReEe[ RjNlS ReElEhM RXDTM RkVgV RaUYW',
	    'PoZFkF R\\NiN R\\RiR R\\ViV RYZlZ RWIW[ R\\J\\Z R\\JiJiZ RVJRN RYCUL RbCaK',
	    'Po[OjO R\\ZhZ RWHW[ R\\S\\[ RbKbS R\\ShSh[ RbDhI RVJRN RbDZK R^KgJlK RYCUK',
	    'Po`ElE R[L_L RWGW[ R[F[V RbJbU RbJkJkU RhXl[ ReVaZ RXCSN RfEeI R_SZU RfLfSeW',
	    'Po[GkG RZMlM RZQlQ RWIW[ R^H_K RaDcF R_Q\\T RVJRN RYCTL R`O_S RgHeM R\\ViZ RhRfUcYZ[',
	    'PoZFlF RYSlS RWHW[ R\\L\\S R_C_I RfCfI R\\LiLiS RVJRN RbIbRdVgYl[ RYCVK RaU^XY[ RbNbU',
	    'PoZQlQ RYVlV Rf[j[ RWHW[ R\\M\\[ RcKcV R\\MjMj[ RVJRN RbFcI RiFkJ RYCUK R]EZJ RhC^E',
	    'PoYHlH Rg[j[ RWHW[ R[L[[ ReLeW RjKj[ R\\O`P R]S_U RVJRN R^D_G RYCVK RhCfG R[LaLa[^[',
	    'Po[ElE R_KjK R_NjN R]QlQ R[ZlZ RWHW[ R[E[[ R_H_N R_HjHjN RXDSN RbN`TjW RhReU]W',
	    'Po[EkE RhPjP RZTlT RWHW[ R[H[P RaHaP RfHfP R[HkHkP RVJRM RcSdVfYl[ RXCUK RcRaW_YZ[ RbE`K',
	    'PoZHlH R\\OjO R\\SjS R[WkW RZ[l[ RWHW[ R\\L\\S R[EcEcZ R\\LjLjS RVJRN RYCUK RjDaE',
	    'PoZFjF R[JjJ RYNkN R]VgV R]ZgZ RWHW[ R]P][ R\\QhQg[ ReNlT R^OYT RVJRN RdJgP RYCUK RaH]Q RbC_N',
	    'PoZKaK RcKkK RZP`P RZU`U RVHV[ RZEZ[ RZEaEaK RcEkEkK ReUk[ RdPgX RWDRO RbOkOhVeY`[ RVFUM',
	    'Po]HkH R]MkM R\\YhY Rf[k[ RWHW[ R\\R\\Z R]D]L R]DkDkL R`MYT RcR_W RWJRN RbTgV RYCVK R]QlPlUk[',
	    'PoYJlJ R\\PjP RZTkT RYXlX RWHW[ R\\L\\P R]T]X RdPd[ R\\EiEiI R\\LjLjP RVJRN RYCVK RaC`J',
	    'Po\\MkM R^WdW Ri[l[ RWHW[ R[Q[[ R\\I\\M R\\EeEdV R\\IkIkM R[QlQl[ RWJRN RhSiX RYCVK RkDcE RhVbV',
	    'Po\\JkJ R]OlO R]TlT RVIV[ RhOh[ R\\EkEkJ RhKgN RaOaW][ R`KaN RXDTM R\\E[QZY',
	    'PoZGlG RZNlN R^ZjZ RWHW[ R^R^[ R_C_J RgCgJ R]SjSj[ R_QZV RVJRN RYCVK RaJ]S',
	    'Po]HjH R]LjL RZOlO RcTjT Rb[l[ RWIW[ R]D]L RcOcZ R]DjDjL R]T_X RVJRN RYDUL R]TZ[ R`ZhZ',
	    'Po\\IjI R\\MjM RZQlQ RWHW[ R\\D\\M R]Q]Z RcDcM R\\DjDjM RVJRN RcQeVhYl[ RYCVK RlSeV ReYZZ',
	    'Po[ElE R\\MkM R]UkU Ri[k[ RWGW[ R\\H]R][ RbPb[ RgPg[ R\\HkHkM R]PlPl[ RXDTM R\\M[X',
	    'Po[FlF R[MlM RWHW[ RcCcF RVJRN RYCVK R^HhK RgGbJ\\K RgO^R RgS^V RjVeY][ R[M[TYZ',
	    'Po^SfS RWGW[ R^L^S R^LfLfS RlWl[ RXDSN R[EiEjVj[ R[E[QYY RfG]H RcHcX\\Y ReUgY_Y',
	    'Po]HiH R]LiL RZOlO RWHW[ R]D]L R]DiDiL R^QZU RaT[Z RdWa[ RVJRN RfRcY RYCVK RaR`V R]SkRj[f[',
	    'Po[IkI R[MlM R]RkR R]UjU R\\ZlZ RWHW[ RdSdZ R[DkDkI RhNlR RVJRN RYCUK RaM_Q R[D[RY[',
	    'Po]JbJ ReJkJ R]MjM R[QlQ Rc[h[ RXHX[ R]D]J R]DbDbJ ReDeJ ReDkDkJ RWIRN RYCVJ R`Q_TjUh[',
	    'Po\\GbG RdNlN R\\UjU R\\ZjZ RWHW[ R\\C\\N R\\Q\\[ RbNaQ ReCeN R\\QjQj[ RlKlN RVJRN RYCUK RjFdH RbLYN',
	    'Po[GgG RYLlL R^UjU R^ZjZ RWHW[ R_P_[ RaCaL R]QjQj[ RfMhO RVJRN RYCUK RjFaN`PXS',
	    'Po^RjR R^VjV R^ZjZ RWHW[ R^O^[ R]OjOj[ RgElJ RVJRN R^FZJ RYCVK RgIeKZP RcCcK`K',
	    'Po]LiL R[ZbZ ReZlZ RWHW[ R[P[[ R]E]L R[PbPb[ RePe[ R]EiEiL RePlPl[ RVIRM RYDUK',
	    'PoZGlG RfYjY RWHW[ RZLZP R\\Q\\Y RcCcG RcNc[ R\\QjQjY RZLlLlP RVJRN R_H_K RYCVK RhHfK',
	    'PoYIbI RZXaX RWHW[ RZPZZ R^D^P RZPaPaX RgVl[ RgUa[ ReQiY RiOeX RXDSN ReCbN RdJfV RdIkIhT',
	    'PoZJlJ R[T`T ReTjT R[ZjZ RWHW[ R\\O\\[ R[EdEcY ReOkOk[ RXDTM R`N[N RiD`E',
	    'PoZFlF R]MiM R\\TjT R_[c[ RWHW[ RZPZT R]I]M RcCcF RcTc[ R]IiIiM RZPlPlT RVJRN RYCVK',
	    'PoZF`F ReFjF Re[i[ RWIW[ R]OiOi[ ReCeLlKlL RVJRN RXCTM R`CaKZL RiU_W R_RfS R]O]V\\[',
	    'PoYGlG RbLhL R\\OkO RYSlS RWHW[ R\\D\\O RbCbL RcQc[ RhDhL RVJRM RcSgXlZ RXCUK RcS^XYZ',
	    'Po\\JjJ R\\NjN R[RkR R\\VjV RZZlZ RWHW[ R\\F\\N RcRcZ R\\FjFjN RWJRN RcCaF RYCVJ',
	    'Po[ElE RaNfN RZRlR RWHW[ R[I[O RaEaN RfEfN R[IkIkO RVJRN RYCUK RaP^VkZ RgTdY[[',
	    'PoZFlF R^JhJ R^NhN RXRlR R[ZlZ RWHW[ R[R[Z R^C^R RhChR RgTiX RaS_X RXDSO',
	    'PoYKaK RWGW[ R^E^[ R]NaR RdW`[ RgCgRjZ RfRbY RkIiO RXDSN R]LXV R]J[S RcIbO RaDYE RfLhV RfLeV',
	    'Po[FkF RYMlM R]UiU R]ZiZ RWHW[ R]Q][ RcDcG R]QiQi[ RWJRN R^H_L RYCVK RgHfL',
	    'Po[HlH RfRkR R\\VkV R\\ZkZ RWHW[ R\\M\\[ RaHaP RfHfQ R\\MkMk[ RhDfI RWJRN R_C`F RYCVK R_R^S',
	    'Po^JlJ R`MjM R_QkQ R^UkU R`[l[ RWGW[ ReCeY R`FjFjM RXDSN R\\RYZ RYF]FYN]N[W RYR[W^Ze[',
	    'PocHiH RbMlM R]SiS R]ViV Re[i[ RWHW[ R]O][ RdDdM R]OiOi[ RVJRN R[EkEkG RYCVK R^H[N R]I`LgM',
	    'PoZGlG RWHW[ R]N`O RdGhKlL RXDSN RaC`G\\KXL RaJ^NZO R_MeLaQZT RfObS\\V RdRjRfW^[ RaTdX[[',
	    'Po^HbH RYRkR RVHV[ RZGZN R^C^N RbPb[ ReCeNkMkN RbRgWlY RXDSN RkFdI RbR\\XXY RcMYN',
	    'Po\\NjN R\\RjR R\\VjV RWHW[ R\\J\\V R\\JjJjV R^FgFcJ RVJRM R_EYK RfXl[ RXDUK R`XZ[',
	    'Po\\DlD R]MjM R\\UlU R\\YlY RWHW[ R\\Q\\[ R]H]M RdQdY R]HjHjM R\\QlQl[ RWJRN RYCVK',
	    'PoaGkG R`LlL R`QlQ Re[i[ RWHW[ R]K][ RfCfL RiNi[ RcTdW R^DYI R_JXP RXDTM',
	    'PoeQiQ R_[f[ RWHW[ R_U_[ RgXf[ R_RdT RhUkY RdM`P R^FYJ R`J[P RXDSN RaF_M R[UYZ RfFbO R]JgM R^C\\HkGiQ',
	    'PoZElE R`OfO R`TfT R[ZlZ RWGW[ R[J[[ R`J`Z RfJfZ R[JlJl[ RXCTM RbE`L',
	    'Po]IiI RZMlM R]PhP R]ThT RYXlX RWHW[ R]D]I R]M]X RhMh[ R]DiDiI RVJRN RYCUL',
	    'PoZGlG R\\PiP RVHV[ R\\J\\P R^S^[ R\\JiJiP RdTjZ RcPfW RaCbG RXCSN RbP_RYU RjReU RcY\\Z',
	    'PofOjO RYTlT RWHW[ R^C^P RVJRN RbSdVfYk[ RYCVK RdNaO RbR`W^YY[ RaEYF RbHYJ RbKYN RgDeM RcDlDkJjO',
	    'Po\\IlI R_RkR R_VkV R_ZkZ RWHW[ R_N_[ RdEdN R_NkNk[ RXDTM R\\E\\RZZ RcE\\F RjD_E',
	    'Po\\JjJ R\\PjP R_[g[ RWHW[ R\\E\\P R`S`[ RcEcP RgXg[ R\\EjEjP RVJRN RiUlZ R`QdS RYCUK R[TYZ',
	    'Po\\JkJ RZMlM Rh[k[ RWHW[ R[Q[[ R\\E\\I RaQa[ RcCcI RfQf[ RkEkI R[QkQk[ RULRN RXDTM RbMaS',
	    'Po[JcJ R[OcO R[UcU Rh[l[ RWHW[ R[E[U R[EcEcU RgFgU RlDl[ RaWcZ R]WYZ RXDTM',
	    'PocFlF R\\NjN R\\RjR R\\VjV RWHW[ R\\J\\V RcCcJ R\\JjJjV RWIRN RfXl[ RYCVJ R`XZ[',
	    'Po\\IkI R\\NkN R^WhV Rg[l[ RWIW[ R[Q[[ R\\E\\M RcEcV R\\EkEkM R[QlQl[ RgSiX RYCUJRN',
	    'Po]JgJ R^[a[ Rf[j[ RWHW[ RZNZ[ RZNaNa[ ReNeW RjNj[ R\\U_V R\\Q_R RcCgHlJ RXDSN RbC\\IXJ',
	    'Po^[a[ RWHW[ RZNZ[ RZNaNa[ R\\Q_R R\\U_V R\\EbDhJ RcTf[ RhTk[ R^KgJlK RfNcT RjNgU RXCSN RbD\\JXK',
	    'Po^UiU R^ZiZ RVIV[ RZFZW R^Q^[ RdQdZ R^QiQi[ R`F\\J R_GdMlP RXCTM R`C^H R_GiGcM\\P',
	    'PocPlP R\\ViV R\\ZiZ RWHW[ R\\R\\[ ReDeN R\\RiRi[ R`JZP ReHkJ RXDSO R\\GbF^M R]C[H]KaNhO',
	    'Po[IlI RZRlR RWHW[ RcCcO R^D_G RiDfG RVJRN RcHfLlN RXCTL RcH^M[O R`P_S\\Vk[ RgTcYZ[',
	    'PobFjF RYMlM R\\QiQ RbUjU Ra[l[ RWHW[ RbCbI RbQbZ R\\IiIiQ RVJRN RXDTL R\\TY[ R\\V_Zg[',
	    'PoWHW[ R`U`Y RgThW R\\D^F RcUeX R\\UZZ RYDVJRN RdD^OYT R\\HhHgM R_MjMiR R[RmRj[f[',
	    'Po[FkF RYKlK RWHW[ R\\O\\W R_H_M RcCcH RgHgM R\\OiOiW ReWl[ RXDSN R`YY[ RbRbW',
	    'Po]NiN RYRlR RcYjY R_ZeZ R[ZcZ RWHW[ RZIZM RcCcI RZIkIlM RfUkZ R\\D_G RjDgG R_U[Z RXDSN R`R^W',
	    'Po[KkK R[PkP ReWkW RWHW[ R[F[P R[FkFkP RcFdPd[lZl[ R^WZ[ RVJRN RjTkW RgRfW RYCVK R_P]X RcNdS',
	    'Po[J`J ReJjJ R[N`N ReNjN RWHW[ R[F[N RcCcP ReEjEjN RVJRN RYCVK R`D[F R^SaWeYl[ R[RjRfWaYY[',
	    'Po[EaE RdHlH RaOgO RdRkR Rh[l[ RWHW[ ReLeR RdFdK RcThTh[ R^KlKkN RlXl[ RXDSN RaZ_[ RcTaZ RiNeN R[EZRYY R^K^S]Z RiD`E',
	    'Po[FkF R\\IjI RZLlL RZTkT RWHW[ RcTc[ RgVkZ RhQlU RcCbL^P RfN`T R^VZZ RVJRN R[PaR RYCUK',
	    'PoZFlF R\\RiR R\\ViV R\\ZiZ RWHW[ R\\N\\[ R\\NiMi[ R[I]K ReHjN RjHgK RVJRN R`HZN RYCVK RbC_J',
	    'PoZFlF R\\MjM R\\PjP RYTlT Rc[g[ RWHW[ R\\I\\Q RcCcQ RgQg[ R\\IjIjQ R^VaY RVJRN RfDiE RYCUK',
	    'Po[DlD R_MkM R_QkQ Rb[e[ RWHW[ R_I_Q ReEdI ReQe[ R_IkIkQ RjTlY RXDTM R_T]Y R[D[QZY',
	    'PoZFbF ReFlF R[JlJ R^NiN R^SiS RZWlW RWHW[ R^J^W R_C_H RhChH RiJi[ RVJRM RXDTL',
	    'PoZElE R[OkO RZSlS RWHW[ R[I[O R`E`O RcOc[ ReEeO R[IkIkO RVJRN RcRhXlY RYCUK RcR]XYY',
	    'Po\\ElE R_HjH R\\KlK R^TlT Rc[g[ RWHW[ R`K`Q RhQh[ R`VbX RVJRN RdLhPlQ RYCUL RdP_P RkLfN R\\E\\QZY',
	    'Po]IiI R\\OjO R\\SjS R\\WjW RWHW[ R\\L\\V R]D]I R]DiDiI R\\LjLjV RVJRN RfXl[ RYCUK R`XZ[',
	    'Po[FlF R[IkI RZMlM RZSlS RYWlW Re[j[ RWHW[ R\\O\\[ R_C_L RcMcW RgCgL R\\OjOj[ RVJRM RXDUK',
	    'Po\\ElE R\\IkI R\\MkM Rf[k[ RWHW[ R`T`Y RdEdR RfTiW RVJRN RcTdX RYCUL R[TYZ R\\E\\RlRk[',
	    'Po[FkF RZPlP RWHW[ RZKZN R_F_K RcCcF RcMcP RZKlKlN RhGfK RYDVJRN RaQ_W\\[ RaTjTh[d[',
	    'Po]IkI R_NiN R_QiQ R_TiT R[WlW RWHW[ R[L[W R]C]H R_K_T R_KiKjT RVJRN RfXl[ RYCVK R`XZ[ RhD^F',
	    'Po[FlF R]NlN R^QjQ R^ZjZ RWHW[ R^U^[ RcCcG RdHdS R^JjJjQ R^UjUj[ RVJRN RYCVK R[F[RZY',
	    'Po[FlF R^NiN R`XgX Rh[l[ RWHW[ R[P[[ R^I^M R`S`Y RcCcG R`SgShX R^IiIiM R[PlPl[ RVJRN RYCVK',
	    'PocGlG RbMlM RZSlS RWGW[ RcOc[ RdGdM RiDiP R[I_K R`K[P RcRhXlZ RXCTM R]D[J R\\GbG_N RbR]XYZ',
	    'Po]EcE RZRkR RYUlU RWHW[ RgNlS RgFkJ RVJRN ReK_Q RbFbJ R`J]MaO RbUeYj[ RYCVK R\\EZK RbRaW_ZY[ RhD_E',
	    'Po\\EdE R\\MlM R`[c[ Rh[l[ RWHW[ R]PcPc[ R\\HlHlL RfPlPl[ RaQ]T RbU^Y RjPfT RkTgX RXDTM R\\E\\RZY RjDcE',
	    'PoYIkI RYOlO RZSiS RWHW[ R_E_[ ReEe[ RZLjLjS RdSkZ R_SYZ RVJRN RYCUK RiDbD[F',
	    'Po]MkM R]QkQ R]UkU R]ZlZ RWHW[ R[F[J R^K^[ ReJeZ R[FlFlJ R_JXP RXCTN RbC]M',
	    'Po_IfI R]OiO R\\RiR R^ZjZ RWHW[ R]L]U^[ R]LiLiR R^UjUj[ RcChIlJ RVJRN R]IYK RbC[J RYCUK R\\O[X',
	    'PoZFlF R[KdK RaNjN R[OeO R[VjV R[ZjZ RWHW[ R[R[[ RcRcZ R[RjRj[ RhLlP RbCcF RXDSO RaH[J RfIZO',
	    'PoRVlV R_H_[ RXL\\M RXQ\\S ReKaN RePaT RYJUO RYOTT RdLiN RdQiS R_CbFlK R^CZGRK',
	    'PoZFlF R[KlK R^RkR R^VkV Rh[k[ RWHW[ R^N^[ R_D_I ReNe[ RgDgI R^NkNk[ RXDTN R[K[TYZ',
	    'PoYHbH RWHW[ R^C^H RhWl[ RZQ`W R]WY[ RgUb[ R`JaN RfQiY RiOeX RXDSN R[JYO R_P[Y ReMhW ReJgU ReIkIhU ReCcN',
	    'Po]ZhZ RWHW[ RZGZJ R]S][ RcCcG R]ShSi[ RZGkGkJ RfIjM R_I[N RcLhRlS RXDTM RbL\\RYT',
	    'Po^OjO RYTlT R\\ZjZ RWHW[ R\\V\\[ RcOcZ RjVj[ R]HaL R_N[R R`FjFeJ RVJRN RYCVK RbEZI R`GcHfJ`MZN',
	    'Po^HhH R^KhK R][b[ Rh[l[ RWHW[ R\\D\\M RZObOb[ R\\DkDkM ReOlOl[ R_O[S R]XYY R`T[Y RjOfS RkSeY RVJRN RYCUL',
	    'Po[GkG R[KjK RYPlP R^TjT R\\ZlZ RWHW[ RcGcO RdTdZ R^C_F R\\VYZ RVJRN RYCVK R_P[Y RgCfF',
	    'Po^LiL R[LeL R][a[ Rg[j[ RWGW[ RYPaPa[ RcPkPk[ RfIjM R^PYT R_SZY RgPcT RiSdY R^DZH RXCTM R`G^L RaEfDjHlH',
	    'Po[DaD RYHcH RcKlK R[KaK R[OaO R[YaY Rf[j[ RWGW[ R[S[[ R[SaSaY RjCj[ RXDSM ReNfT',
	    'PoYIaI RcIlI RaZlZ RWGW[ R]C]I RePeW RgCgI RXDSM RbXXY RZL[T RdLeU RjLh[ R`L^X',
	    'Po[KkK R_[c[ RWHW[ R[F[J RcCcF R[FlFlJ RgTlY RVJRN RjNgQ RdMhV RdKfT RYCUK RbM[P RaQZT R_NcS`WZY RbPdTdWdZ',
	    'Po[FjF R[JjJ RYNlN R_[b[ RWHW[ RbPb[ R]R_T RfMlS RhReT RVJRN R_NYS ReJgO ReWkZ RYCUK RaG]P RaWZY RbD_N',
	    'PoZFcF RXJlJ RXNcN RZScS RVHV[ R_C_J R_P_[ RlXk[ RiDjG RjOeZ RXCRO R]KZS RjLgV RfCfMhZ RbWYX',
	    'Poe[l[ RWHW[ RYIYM RbCbI R]N]SgTe[ R]NhNhS RYIlIlM RlXl[ R\\E]H RiDhH RXDSO RaT_X]ZY[',
	    'PocHiH R]SiS R]ViV Re[i[ RWHW[ RZLZP R]O][ R]D]L R]DiDiL R]OiOi[ RZLlLlP RVJRN RYCUL RcHbL',
	    'Po^KeK R^OjO RYUlU RWHW[ R^K^U RfOfU R[FlGlJ RVJRN RbCdF RgXkZ RYCUL R_XZ[ R[G[J RgJ`J',
	    'Po`GiG R_LlL RbUkU RbYkY RVHV[ RXL\\L\\Z RbOb[ RdCdL R`QkQk[ RZE]H RbP^R RkEjHaQ RXCSO R_X[Y',
	    'PoYG_G RaJlJ RbOkO RbTkT RbZlZ RVHV[ RbHb[ RhIhZ RgDhH RZM_W RXCSN R]NYX RdC`L R^I[U',
	    'Po\\HiH RZHdH RdMkM RYN_N RWHW[ RiKkM RfEjI RbJgOlR RXDTN R[IZM R_C^H RfIeN RbJ\\PXR RbP[S ReS\\W RhVcYZ[',
	    'Po[HjH R[LjL R\\PdP RbTjT R_[c[ RWHW[ R[D[L RcDcL R[TdTc[ R[DjDjL RgVkZ RhQlU R^VZZ RVJRN RaL]P RYCVK RgNZT',
	    'Po\\IkI R\\NlN R\\RkR R\\VkV R\\ZlZ RWHW[ R\\E\\I R]M][ RcCcI ReLeZ RkEkI RVJRN R^J\\MYQ RYCVK ReJdO',
	    'Po[FlF R\\LlL R^OjO R]UkU R]XkX Rh[k[ RWHW[ R]R][ RdFd[ R^IjIjO R]RkRk[ RXDTM RcDdJ R[F[RYZ',
	    'PoZFlF R[MkM R[PkP R]VjV R]ZjZ RWHW[ R[I[P R]S][ R`C`P RfCfP R]SjSj[ R[IkIkP RXDTN',
	    'Po\\QjQ RWHW[ R\\F\\Q R\\FjFjQ RjVlZ RVJRN RbCaF RbH^KeM R`SdT RYCUK R\\UZZ RaJfJcN_O R`U_[gYg[',
	    'Po[IlI R_[a[ Ri[l[ RWGW[ R[E[I R[LbLbZ RcCcI RlElI ReLlLl[ R]O`Q R]T`U RgTjU RgOjQ RXCTM R[L[TZZ ReLeTdZ',
	    'PoeSkS RdZlZ RWHW[ R\\K\\[ RfNfZ RbWeZ RbGeJ RhHlL R]EYI R\\LXP RaRbY RXDTM R]H[N RbD`L RiDgK RaP_Z',
	    'PoZGbG RYKcK RYPcP RWGW[ R^C^K RXDTM Ra[][ R^L]P[Y R\\TaTa[ ReIlI RkIgTfWd[ RgEdM ReLhTm[',
	    'PoZFlF R\\LjL R\\OiOkS RZRkR RYUlU Rc[g[ RWHW[ R\\H\\O RcCcR RhRh[ R\\HjHjO R^V`Y RVJRN RYCUK',
	    'Po[ElE R`MiM R^VcV RfVkV R[YlY RWHW[ R[E[[ R^P^V R`H`M R^PcPcV RfPfV R`HiHiM RfPkPkV RXDTM',
	    'Po[EkE R\\HjH RZJlJ R\\PjP R\\SjS R\\VjV RWHW[ R\\M\\V RcCcJ R\\MjMjV RVJRN RfXl[ RYCUK R`XZ[',
	    'Po[GlG R`LdL R\\PdP RWHW[ RhDjF RfWcZ RVJRN RcSdV RjOeY RYCVK R]S\\X RjLgV RaI`Z^Z RfCfMfOhY R[G[SY[ RlWk[i[',
	    'Po]FkF R]KiK R]NiN RZQlQ RWHW[ R]H]N R]HiHiN R`QZW RaU\\Z R^C[I R]TkTj[f[ RYDVJRN RgUeXa[',
	    'PoYGlG RVHV[ RZJ^L R[P^T RfPjT R_OZU RiIeN RjOeT R^IZN RbCbQdVhYl[ ReJjL RXDSN RbP`V]YY[ RbMaT',
	    'Po[FkF RZKlK R\\QiQ R\\TiT Re[l[ RWHW[ R\\N\\T RcCcF RfTfZ R\\NjNjT RlXl[ RVJRN R_G_K RYCUK RgGfJ R_W]YY[',
	    'PoeFkF R^NgN R[SjS R_[b[ RWHW[ RbSb[ RYJ]K RgVkY RfJkP RjGfK R\\U[Y R]FXJ R`JZP RcDgL R[H`I RXDTM R]C[I R\\FaF^L',
	    'Po[MjM RWHW[ R[F[M R[FjFjM ReMeQkPkQ RbC`E R_HfK RXDTN ReH^K R_M_PZR RaQ]UYV R^TbXeYl[ R_ThSeWaYZ[',
	    'PoZFlF R^IgI R[PjP R[SkS RZVlV RWHW[ R[L[P R^C^I RhChI R[LjLjP ReXl[ RXDTM RcIcV`Y[[',
	    'Po[FjF RZKlK R\\QiQ R\\TiT RYWlW RWHW[ R\\M\\T RcTc[ RcCcF R\\MiMiT RVJRN R^G_J RYCUK RfGfJ',
	    'Po_ElE R`MkM R`RkR R`VkV RZW^W RWGW[ R[F[V R`I`V R`IkIkV RhXkZ R^JZN RXDSN ReEdH R^S^W RcX][',
	    'Po[ElE RcHkH RZLlL R\\PlP Rf[j[ RWHW[ R_U_Y RcEcL RVJRN RcUcY RfUgX RYCUL R[UYZ R^H^K[TlTk[',
	    'PoYGlG R`KdK RhLlL RfZiZ RWGW[ RYNYQ R\\R\\Z R]C]I R`C`K RbNb[ RdCdK RhChL R\\RiRiY RYNkNkQ RXDSM R[LYL',
	    'PoYKlK R^[g[ RWHW[ R_T_[ R[EdEcP RgXg[ R]G^J RhFgI R]OYQ RbJ\\P RbRcV RiTkY RbLkP RXDSN R[UZY RiDbE',
	    'PoYHlH R\\KjK R[QjQ RYTlT RWHW[ R[N[Q R\\E\\K R\\EjEjK R[NjNjQ RXDTM RcCbQ_T]WlZ RhUdYZ[',
	    'PoZFlF R[RfR R[VfV Rc[f[ RWHW[ R[O[[ R_D_I RaOaZ R[OfOf[ RgDgI RXDTM R\\IYO Rj[g[ R[LlKkVj[',
	    'Po[ElE R[NkN R\\QjQ RZTlT R_[c[ RWHW[ R[H[N R`E`N RcTc[ ReEeN R[HkHkN RhWkZ R]WZZ RVJRN RYCUK',
	    'PoZFlF R_JhJ R\\PjP R[TkT R\\WjW RZ[l[ RWHW[ R\\L\\P R_C_J RcJc[ RhChJ R\\LjLjP RVJRN RYCUK',
	    'Po_FlF R`NkN R`QkQ R^UlU Ra[l[ RWHW[ R`J`Q ReCeY R`JkJkQ R[E^H RZM]M]XZZ RXDTN',
	    'PoWHW[ RZEaEaL RdEjEkM RcNlR RXDTN R_I]KZL RgFcH RhJdL RaNYR RcQ\\T RfS]W R^FZH RcNaM RhVcY\\[',
	    'Po]GjG R]JjJ R[QkQ RWHW[ R[M[Q R]D]J R]DjDjJ R[MkMkQ RVJRN RYCVK R`MaQ RfMfQ R]S`WdYl[ RZTjTgVbYZ[',
	    'PoVS]S RaSiS RVNVS RVN]N]S RaNaS RaNiNiS R_CeI RYVT[ RcWj[ RXW]Y RYJeJlL RdU_Z][ R^CXJRL',
	    'PoZEkE R[MjM RYPlP R]WgW R^[i[ RWHW[ R[H[M R^R^Z ReEeM R\\ShShW R[HjHjM RePjU R`OZU RiXj[ RXDSN R`E`M^R',
	    'Po\\QiQ R\\TiT R\\WiW RWHW[ R\\N\\W R\\NiNiW RbGcI R]KhJdN ReXk[ RbDlI RXDTM R_XZ[ RbC_GZI',
	    'PoYFbF RYNaN RYSaS RXVbV RWGW[ RYJYS R]C][ RYJaJaS RhWl[ RgUb[ RfQiY RiOeX RXDSM ReCdLgV ReIkIhT',
	    'PoZOdO RZSdS RYVdV RWGW[ RZKZR RZKdKdR RXDSN RgCgPfW RcDZE RYIcI RfImImSj[g[ R^E^Z RZZdY',
	    'Po[HaH RdHkH R[MbM R[QkQ RYVlV RWHW[ R[D[L R_N_V R[DaDbH RfNfV RdDkEkI ReDeMkLkM RfXl[ RXDTN R_XZ[',
	    'Po[MkM RWHW[ R\\H\\M RZIkIkM R_DYJ RXDTN RcMZR RjRfT RdTaWYZ R`OdUd[`[ RcSlZ R^EgEdH RdIdN RbSZV',
	    'Po[FkF R\\JjJ RZMlM RYRlR R\\ZjZ RWHW[ R\\U\\[ RcFcR R\\UjUj[ R]N^Q R^D_E RhNgQ RVJRN RhDfE RYCVK',
	    'PoZHlH R^OgO R_XfX Rf[j[ RWGW[ R[R[[ R_J_O R_T_Z R_TfTfX R]LhKgO R[RjRj[ ReHjN R`HZN RXDTM R[EbE^L RhD`E',
	    'PoYOlO R[RiR R]ZjZ RVHV[ R^T^[ R[LiLiR R\\VjVj[ RZFZGkFkH RaCcF R^SYX ReHjI RXCSM R`N]V R`H[J R`L^T',
	    'Po\\IjI RZLlL R\\QjQ R\\TjT R\\WjW RWHW[ R\\N\\W RcCcL R\\EjEjI R\\NjNjW RVJRN RfXl[ RYCUK R`XZ[ R\\E\\I',
	    'PodNjN R[PjP R[RjR RYUlU Rb[f[ RWHW[ R[J[R R`G`M R]GfGeM RfRf[ R[JjJjR R\\W_Z R_DgElJ R\\EXJ RXDTN',
	    'PoZIlI R[NkN R[QjQ RYUlU RWHW[ RaCaI ReCeI R\\D]G RiDhG RVJRN R^J_M RcNcUfYl[ RYCUK RgIgL RcU_YZ[',
	    'PoZFkF R[IjI R\\OjO RYSlS R\\ZjZ RWHW[ R\\V\\[ R\\K\\O RcCcI R\\KiKjO R\\VjVj[ RVJRN RfQfS RYCVK',
	    'Pod[i[ RWGW[ R[NZR R[NkNkR R^HaK RgHkL RdEeG RkEjG R[E\\G RXDTM RaEaG R]K[L RfKcL RaV^YZ[ R_D]K RbPaW RhDgJ R\\SjSi[',
	    'Po_[b[ RVHV[ RYKY[ R\\O\\X R^C^K RYKbKb[ RhVc[ RfRjZ RaEbJ RjPfY RXDRO RZEXK RfCcN R\\O`O`V\\V ReJhW ReIkIhV',
	    'Po[FlF R_IgI RZLlL R\\SjS R\\WjW RWHW[ R\\O\\V R_C_I RcLcV RgCgI R\\OjOjV RVJRN RfXk[ RYCVK R_XZ[',
	    'PoYGkG R\\PhP R\\ThT R^[b[ RWIW[ R]K]T RbTb[ R]LhLhT RgVkZ RZH]K RdIkN RjHgK R]VYZ RXDUJRN RaDaIZN',
	    'PoYHbH R[L`L R[P`P RXTbT RVHV[ R[C[T R`C`T R`VaY RgSkZ RfRcZ RXDSO R[VY[ RdHkHjN RgHfPeW ReCcN RgLhV',
	    'Po^LfL R\\ThT RY[k[ RWGW[ R\\O\\T R\\OhOhT RZG]J ReHkM RkGhI R_HYN RiDeG R^V_Y RcCfJ RfUeY RXDSN R[E`E^J',
	    'Po[HkH R]MkM RaPdP RdWlW R\\[_[ Re[h[ RWGW[ R_U_[ RhUh[ R[DjDkH R\\SbS_U ReSkShU RXDSN R_JiJdLdP RbV[W R[D[RYY',
	    'Po\\EdE Rf[k[ RWHW[ RaVbZ R\\JgJgO RhUiW RhFkJ ReVeY RbFbI RXDTM R\\EZK R^V]Z R\\OjOiS R\\J\\TlSk[ R\\O[X RiD`E',
	    'PoYQlQ RWHW[ R]SaW RZG]J ReGhJfN RlXk[ RgOjP RgZj[ RjFgL RXDTM R]D[H R_F[N RhDfH R[ZXZ RiSdX_[ R]Q[Y R_K`NYN RcCbKdW RjKkNdN',
	    'Po[KlK R]UjU R]ZjZ RWHW[ R]Q][ RdDdZ R]QjQj[ R^G_I RcKhM RiNkP RWJRN RYCVK RhFfK RbK_NZP RiC^E',
	    'PoZFlF R\\LjL R\\OjO RZSjS RWHW[ R\\I\\O RcCcR R\\IjIjO RhVlZ RhQlT RVJRN RaTeV RYCVK R\\VZZ R`V`[gZg[',
	    'Po[EbE RcPkP RaVlV RWHW[ RYKYN RcPcV RhMh[ RZKkKkN R[S^VY[ R\\F]I RbFbI RXDTM R\\MYT R[P`Q]X RiFgK RiCaD',
	    'PoZFbF RcFkF RZJbJ RcJlJ R\\ViV R\\ZiZ RWGW[ R\\R\\[ R\\RiRi[ R^C^KaN RhLlP RgCgLcP R]MYQ RgIiN RXDSN R^F]N',
	    'Po[IaI ReIkI R\\PkP R\\SkS RZWlW RWHW[ R[D[I R\\L\\S R[DaDaI RcLc[ ReDeI R\\LkLkS ReDkDkI RVJRN RYCUK',
	    'Po\\FjF RZJlJ RZMaM RcMkM RcQlQ RYTlT Re[l[ RWHW[ R^K^Q RcCcJ RfTf[ RgKgQ RlXl[ RXDTM R`T^YY[ RaQZR',
	    'PoYGcG RcJlJ RZQaQ Rh[l[ R[[^[ RWGW[ RZKZQ R^C^G R^Q^[ RZKaKaQ RgCgPh[ RlXl[ RiElG RaTbW RfScZ RXDSN RZTYY RfLeW',
	    'Po[KlK R[OlO R]VkV R]ZkZ RWHW[ R[G[O R]R][ RdGdO R]RkRk[ R[GlGlO R^C`F RjCgF RWJRN RYCVK',
	    'Po[EjE RZIlI R\\PjP R\\TjT R\\WjW RWHW[ R\\L\\W R^G^K RcCcG RgGgK R\\LjLjW RVJRN RfXl[ RYCUK R_YZ[',
	    'Po[HbH ReHlH R[MbM ReMlM R`SgS R`VgV Rh[l[ RWHW[ R[E[[ R`P`X R[EbEbL ReEeL ReElEl[ RXCTM R`PgPgYeY',
	    'PoeSkS R`YgY Rh[k[ RWIW[ R[P[[ R`U`Z RePeS R`UgUgY R[PkPk[ R[IlIiL RVJRN RXDTM R]EjEdG RbKZN RaP_S\\T RaFfHeNbN',
	    'Po[ElE R[I`I ReNjN RZRlR R_[g[ RWHW[ R_U_[ R`E`I ReIjIjM RaSdV RhUkX RVIRM RXCUK R[UYZ RgXg[ R[I[N`N`R ReEeI ReNeR',
	    'Po\\IlI R\\LjL R\\PjP R\\UlU RWHW[ R]G]U R`W`[ RdGdU RjWk[ R\\HYK RdWe[ RXDTN R^C[J R[WY[ ReDdJ',
	    'PoZFbF RcFlF RYKaK RcKlK R\\VjV R\\ZjZ RWHW[ RYHYK R\\R\\[ R_J_O RcHcK R\\RjRj[ RfLcP R]MYQ RXDSN RbN^N RiJhPlNlP R^D]M RgDfM',
	    'Po[FkF RZJlJ R\\PjP R\\TjT R[WjW RY[l[ RWHW[ R\\M\\T RcCcF RcMc[ R\\MjMjT RVJRN R_G_I RYCVK RgGgI',
	    'PoZHlH RdQlQ RbWlW RWHW[ RcCcN RdQdW RiOi[ R\\D^F RiDfG R]QYT R[S_V[[ RcGfKlM RXDTN R\\RaQ^X RcG_KZM',
	    'PoSE^E R`ElE RTHTM RYCYT RaHaM ReCeT ReLjQ RYLSQ RXM]O ReLbP^Q R`UcXfZk[ RTH]H]M[M RaHkHkMhM R_T[XXZS[',
	    'Po[EcE R[LkL R^PlP R^SkS R^VkV R^ZlZ RWHW[ R_O_[ ReNeZ R[HkHkL RXDTM R`L]R ReLeP R[E[SY[ RjDbE',
	    'PoZElE R[MkM RdQlQ RbVlV RWHW[ R[H[M R`E`M RdQdV RfEfM RiNi[ R[HkHkM R\\QYT R[S_V[Z RXDTN R]N[S R\\QaQ^X',
	    'PoZElE RZNkN RYTlT RVHV[ RZIZN R`E`N RbPb[ ReEeN RZIkIkN R]P^S RbSfWlZ RhPfT RXDRO RbS^XYZ',
	    'Po[HaH ReHlH R[LaL ReLlL R_TgT R_WgW Rh[l[ RWHW[ R[D[[ R_P_Y R[DaDaL ReDeL R_PgPgW ReDlDl[ RXDTM',
	    'PoZElE R\\JjJ R\\MjM RZOlO R[UjU R[XjX RY[l[ RWHW[ R[R[X R\\G\\M RcGcM RcRcX R\\GjGjM R[RjRjX RXDSN',
	    'Po[JaJ RdJjJ RZMaM RZRaR RdRkR RZWaW RdWkW Ra[l[ RWGW[ RZMZX R[E[I RaEa[ RdEdY R[EjEjI RdMkMkV RlXl[ RXDTM',
	    'PoYFlF RYKlK RbNhN R\\QkQ RYTlT RWHW[ R\\I\\Q R_C_H RbHbN RcQc[ RfCfH RhHhN RcSgXlZ RXDTN RcS_XYZ',
	    'PoZFbF RcFlF R\\OiO RZSlS R`[c[ RWHW[ R_C_N RcSc[ RgCgN RhVlZ R^FbJ RfFlL R^VZZ R]HZL RXDSN R_E\\J RgEdK',
	    'PoZElE R[LkL R\\QjQ R\\TjT R\\WjW RWHW[ R[H[L R\\N\\W R`E`L ReEeL R\\NjNjW RfYl[ RXDTM R[HkHkL R`YY[',
	    'Po^IhI R[PaP RePkP RYTlT RWHW[ R[K[P R^D^I R[KaKaP RcRc[ ReKeP R^DhDhI ReKkKkP RVJRN RcSgXlZ RYCVK RcS^XYZ',
	    'PobGlG RZL`L RbNlN RcTlT R[YaY RWHW[ RZE[R[[ RZE`EaL R[QaQaY RdIeM RgCgG RgNg[ RjIiM RXDSN RZNYY',
	    'Po\\IjI R\\LjL RZOlO R\\ViV RZ[l[ RWHW[ R\\E\\L R\\R\\V RaCaL ReCeL R\\RiRiV R\\EjEjL RVJRN R_W`Z RgWeZ RYCVK',
	    'PoYHbH R^[g[ RWHW[ R^D^R R_T_Z RgXg[ R^JaM ReMaQ RkGjJ R\\KYO RbSdV RiTkY RcGdK RiLjNkQ RXDTN R^G[M R[UYZ RaDZE RgCgIeN',
	    'PoZEaE R_QgQ RWHW[ RZJZM R_M_P RZJkJkM RiMlP RjFgH R`Q[V R\\M[P R\\F\\J RaFbJ RXDTN RaKdL R^TaWeYl[ R^ThTeWaYZ[ RiC`D',
	    'PoZJjJ RZMjM RYQlQ R]VhV R[[k[ RWHW[ RZFZH RbCbF RbSbZ RZFkFkH RgHfRkW R_H^QYW RXDSN',
	    'PoZFkF R[IjI RYMlM RYRlR R[[^[ RWHW[ R^O^[ RbFbM RhNjQ R^C_F RgCfF RaNYO RbUYW RYCVJRN RdOfYj\\k\\mY RjTc[',
	    'Po\\GkG R\\JjJ R\\MjM R\\PlP RaWfW Rg[l[ RWHW[ R[S[[ R]F]P RdEdP RfSlSl[ RVJRN R]GYJ RYCVK R]C[I R[SaSaW RfSfW',
	    'Po[IjI R[LjL R[OlO R^QjQ R[TlT RWHW[ R[F[L R_T_Z R`C`L ReCeL R[FjFjL RcUgYl[ RXDTM RjVfX R[O[TYZ ReZ\\[',
	    'PoZFlF R_MfM R\\PjP R]UiU R]XiX RZ[l[ RWHW[ R\\H\\P R]R]X RcCcF R_K_LfKfM R]RiRiX R\\HjHjP RVJRN RYCUK',
	    'Po[FkF RYKlK R\\QiQ R\\TiT RWIW[ R\\M\\T RbCbF R\\MiMiT RaUeW RhWlZ R\\WYZ R^G_I R`V_[g[gZ RgGfI RXDVJRN',
	    'Po]OiO R\\VjV R\\ZjZ RWHW[ RZHZK R\\R\\[ R]K]N RcCcH RcRcZ R]KiKiN R\\RjRj[ RZHlHlK R]D^F RiDhF RXDTM',
	    'PoXFcF RYU^U R\\[`[ RVHV[ RYPYW R[C[H RYP^P^U R`C`I RgVlZ RgVb[ RePiY RiPeY RWERN R[JXP RdJgV RdIkIhV RVEUL ReCcO RZMaLaV`[',
	    'Po[IjI RYLkL R\\SiS RWHW[ R[D[I R\\N\\S R_U_Z R`E`I ReEeI R\\NiNiS R[DjDjI RdUgYl[ RXDTM RcSYX RjUeW ReY\\[',
	    'Po[QjQ R\\WiW R\\ZiZ RWHW[ R[K[Q R\\S\\[ RcKcQ R\\SiSi[ R[KjKjQ RcCgG RVJRN R_M_O R_HfHlI RfMeO RYCVK RbC^HYJ',
	    'Po[SaS RdSjS RWHW[ R[M[S R[MaMaS RdMdS RdMjMjS RhXk[ RfWiY R]WaX RcChI R^JgIlK R]WY[ RfWb[ R\\IXK RbC[J RgTdZ RXDSN R^T\\Y',
	    'PoZFbF RdFlF RcUkU Ra[l[ RWHW[ R_C_N RdPdZ RhChN R]FaJ RfHcL RVJRN R_FZL RjHkL RZPkPjS RYCVK RhEeK R^U^X R]S[Z',
	    'Po\\IlI RfLkL R_NiN R\\QlQ R_SiS R_UiU R_ZjZ RWHW[ R_W_[ RfIfL R_WjWj[ RVJRN R^FYK RaJ^M R^EfEdH RYCVK R\\I\\R[Y',
	    'PoZJbJ RZMbM RYQcQ RWHW[ RZFZM R]C]F RZFbFbM RgVl[ RgVc[ RfRiY RiQeY RXDTM Ra[][ ReJgV ReIkIhV R^M]Q[Y RfCdN R\\UaUa[',
	    'Po[LjL RWGW[ R[D[L RjDjL RdHgK R_EcG RfGhH RaDcD ReQkPkQ RXCTM RfDbH]J R`L^OYR R`SYV ReLeQ RaQ]TaWfYl[ R^ThSdW`YY[',
	    'PoYFdF RXIlI RYMdM RYRcR RYUcU RXXdX RWGW[ RYOYU R_C_I R_K_[ RYOcOdU RlYk[ RiDjG RjNeZ RXCSN RjLhW RgCfMhY',
	    'Po\\HaH RZTlT RWGW[ R_D_H RcPc[ R\\K_L RhJkM R^XYZ R^Q`T\\Y RiQfT RdThXlZ RdHhJ RXDTN R\\GZK RaJ]NYP RcFkEiIfLbM R_EaE^F',
	    'Po^IiI R^RjR R^TjT R^WjW RWHW[ R[F[H R_O_W RcCcF RcIcM R[OjOjW R[FlFlH RVJRN RgYlZ RYCVK R_KZM RiJdM[O RaYZ[',
	    'Po[HjH R[KjK RdUkU RWHW[ R[D[K R`W`[ R[DjDjK RjRkU RfMcP RiOcU RVJRN R]MZP R`OZUaTaU RdWf[ RjWl[ RXCTL R[WY[ R_PZQ RiPcQ',
	    'Po[DjD R\\J`J RdJiJ R[M`M RdMjM RYPlP RVIV[ RZGZJ R[S[[ R`S`[ RbPbS RbDbN ReSe[ RZGkGkJ RXCTM R[SkSj[h[',
	    'Po[FlF R\\HjH R]MiM R\\RiR R\\UiU R\\WiW RZ[l[ RWHW[ RZOZR RcCcH RcTcZ R]J]LiJiM RZOlOlR RaR^T RXDTN',
	    'PoZElE R[HjH R\\MiM RZOjO RYRlR RbUlU RZY`Y Re[i[ RWHW[ RZTZZ RZT`T`Y RbMbP RcCcH RiSi[ RcWeY RYKkKkM RXDTM',
	    'PoZFlF R\\O_O R\\WiW RWGW[ RbDbF RbKbO RfIfO R\\SiQi[ ReHdI RiIkO RXCTM R\\IZO RkIfI RiNeN R\\Q[Y RZI`H`I_O',
	    'PoZEbE ReHlH RZLbL ReOlO RZPcP RY[l[ RWHW[ RZEZP R[S[Z R^E^H R`S`Z RZHbHbL ReSeZ R[SjSjZ RXDSN RfDdK R^L^P',
	    'PoZKaK RZOaO RfSkS RXTaT RWGW[ R[C[J RgMgZ RbMkMjQ R]N]U`X RdGhI RbDkDgI R\\WX[ RXDSN R[LYR R`EZG RcQaZ RcUeZl[ R]P\\X',
	    'PoYIlI R[LiL RYOlO RYZlZ RWHW[ R[U[Z R`U`Z RbCbO ReUeZ R[EiEiL R[UjUjZ RiQlT R[QYT R`Q_T RdQeS RXDSN',
	    'Po`IjI R_KlK RaQjQ RaXjX R`[l[ RWGW[ R`E`I RaNaX ReCeK RaNjNjR RaTjTjX R`EjEjI RZJ\\L RZD]F RXP]P\\XX[ RXDSN',
	    'PoZFlF R[MkM RWGW[ RZPZS R[I[M R_C_G R`I`M ReIeM RfCfG R[IkIkM RZPlPlS RXCTM RaSZV R_ThSeW^Z R^UcXZ[',
	    'Po\\GkG RYJlJ RZOlO RdRkR RbWlW RWHW[ R]E]O RaFaO RdRdW RdFdO RhPh[ RhFhO R]RYV R`U[[ R^DZH R[U_W RXDTN R\\SaS_X',
	    'Po_LfL RYSlS RYUlU R^[b[ RWHW[ R\\N\\S R`H`L R`N`S RbCbF RcUc[ ReNeS R\\NjNjS RZFkFkH RhIkL RaGdI RXDTM RZF[I[L',
	    'PoZSkS RWHW[ RZNZQ RcPc[ RZNkNlQ R^H`K RgHjK RjEiH RaF`H RcRhXlZ RXDTM R[F[I RdEcH ReKcL RbR]YYZ R^D]J RgDfJ',
	    'PoZElE Rf[j[ RWHW[ R[K[[ RbEb[ R[KjKj[ R^T`W R^N`O]Q ReNgPdQ ReTgVdX R^GZJ RfGlJ RXDSN R`S^W',
	    'Po]MhM R\\RjR R\\TjT R\\WjW RWHW[ RZGZJ R\\O\\W R]J]M RcCcG R]JhJhM R\\OjOjW RZGkGkJ RhDgG RVJRN R]D^G RfYl[ RYCVK R`YY[',
	    'Po[PjP RWHW[ R[J[P R\\R\\[ RcRc[ R[JkIkP R[F^I RaFdH RgFjI RjCgF R]C[F RdCaF ReSgS ReVgV R^SaS R^V`V RlYl[ RXDSN RfL^N R`Z[Z R^KhN RgYb[ RiSj[',
	    'Po\\IiI R\\MiM RYUaU RdUlU RYYaY RdYlY RWHW[ RYPY[ R\\E\\M R]P]Y RYPaPa[ RcEcM RdPd[ RhPhY R\\EjEjM RdPlPl[ RXCTM',
	    'PobEkE R\\KgK RbMiM R^RjR R^UjU R`[h[ RWHW[ R^O^U RbCbM RdOdU R^OjOjU RcVeX RiWlZ RZHlHkJ RXDSN R\\W\\[ R`W`[ RZHZSY[',
	    'Po[FlF R[JlJ R`MgM R[OlO R]TjT R]XjX RWHW[ R]Q]W R`G`L RcCcF RdOdW RgGgL R]QjQjW RfYlZ RXDTM RaY[[ R[F[QYZ',
	    'Po[GiG R[JiJ RZMjM RYQlQ R_[b[ RVIV[ R[D[J RbSb[ R[DiDiJ R]T_V RfJfQhTfV RgSlU ReXkZ RXDTM R_J_Q\\TXU R_XZZ',
	    'Po[ElE R\\HkH R\\NkN R]SjS R]UjU RWHW[ RcCcH R]PjPjX R\\JkJkN RVJRN R]P]XhXlZ RYCVK RaJaN RfJfN R\\J\\N R`YY[',
	    'Po[FlF R[MkM R^PcP RWHW[ R^N^U RaFaM RbWb[ RfFfM R[IkIkM RfNfUkTkU RjVkZ RcCdF RfWf[ RXDTN R]W\\[ RkPeQ RbS]T R[FZSY[',
	    'Po[FbF RdFkF RZIbI RdIlI R\\QiQ R\\TiT R\\WiW RWHW[ R\\N\\W R\\NiNiW R]IaL RhJlM RgJcM RVJRN R^JZN ReXk[ RYCUK R`YY[ R^E]K RgEfK',
	    'Po\\GcG ReHlH R\\PjP R\\SjS R\\WjW RWHW[ R\\M\\V R`G`L RiGiL R\\MjMjV RVJRN RfXl[ RYCVK R`XZ[ RaD\\E[L RjDdEdK',
	    'PobIkI RZJ_J RbMkM RZO_O RZT_T Rg[k[ RWGW[ RZDZT RZD_D_T RbDkDkM ReSaW R^V_Z RjQfY RXDSO RZVX[ RbDbMbRlQlVk[',
	    'PoZEkE R]JiJ R]LiL RWHW[ RZNZQ R]G]N RbEbG R]GiGiN RZNlNlQ RhPkS R]P[S RXDTM R^VcYk[ R_QaShS R^UZX R^UhUbY^ZY[',
	    'PoZFlF R]KiK R\\PiP RWHW[ RZNZP R]H]K RbCbF R]HiHiK RZMkMlP ReTjY RdPfV RXDSN RcP[S RaT[W RjSfT R`RcTcW[Z RdWc[_[',
	    'PoYEbE RYHaH RdIkI RXLbL RdNkN RYUaU Ri[l[ R^[a[ RVHV[ RYOY[ R]C]L R]O]T RYOaOa[ RdDdRjRi[ RdDkDkR RlXl[ RXDRO RfRdZ',
	    'PoYGaG RdIjI RXMbM RdNjN RXQbQ Rh[l[ RVHV[ R[H[L R]C]F R]N][ RdDdRjRi[ RdDjDkR RlXl[ R]RaV R\\TXX RXDSN R_H_L R]P[V RcZa[ RfRdZ',
	    'Po[ElE R\\KcK ReKlK RdUkU R[ZlZ RWGW[ R_T_Z RaHaQ RdSdZ RiHiQ R_KcO RhLlO RhIfO RXDSO R`J]O RcG\\H RkGdH R[EZRYZ',
	    'Po]GhG RYKlK R[OgO R\\QhQ R\\ShS Re[i[ RWGW[ R_X_Z RbNbU ReWfX ReJkO RfDjH R[WZ[ R]DZH R_JYP RbWcZ ReGfL RXDSN RaD]M]VjVi[',
	    'PoZFlF R[JdJ Rf[k[ RWHW[ R_C_H RaVaY RfCfG R\\NgNfQ R\\QiQiT RhVhX RiJhL RdVeY RbKbL RXDTM R\\N\\V]Z R[UlTk[ R[OZY RiHbI',
	    'PodFkF RYFaF RYLbL RdNjN RdQjQ RZSaS RdTjT RZVaV RdWjW RVGV[ RZOZ[ R[H[K R]C]F RdCdIjIkM ReNd[lZl[ RXDRO R`H_K RZOaO_[`[^[',
	    'PoZE`E RbGhG RYHaH RZL`L RbLlL RZO`O RdUkU RZY`Y RdZkZ RWGW[ RZSZ[ RZS`S`Y RdQd[ RfCfL RdQkQk[ RhNjO RhKaR RkDgL RXDSN',
	    'Po[JjJ R\\LaL RcOkO R[WjW RWHW[ R\\F\\J RdKdO RZGjGjJ RZTjSjW R^CYH R]EdEbG RfXiY Rc[lZl[ RXDSN R\\K[O_OZS[W RcWc[ R]QdQaW_YY[ RjLcM',
	    'PoZFkF ReLjL R[NkN R[QjQ RZTkT RWHW[ R_V_Z R\\H\\L`L_T R\\HaHaL RcCcF ReHeLfT ReHjHjL RVJRN RcUgYl[ RYCUK RbUZX RjVfX RdZ][',
	    'Po`FlF R^RjR R^TkT R\\WlW RWHW[ R[F[V R^N^R RbXb[ RdNdW R^NjNjR RiXl[ R^Y\\[ RbD]I RfXf[ RXDTM R`FbIfKlL RiFfIbK\\M',
	    'Po[GkG R\\LjL R\\OjO R\\QlQ R`ZgZ Rh[l[ RWHW[ R[S[[ R[D[G R]J]R R`W`[ RdCdG RdIdQ R`WgWfZ RkDkG R[SlSl[ R^HZM ReTjV RXDSN RaU]V',
	    'PoZDbD RdDlD RZMlM RZSkS R^VcV RWGW[ R[G[K R^S^[ R^H^K R`M`S RhHhK RZOkOkS RfMe[lZl[ RXDTM RkVeW ReGeK ReGkGkK R[GaGbKcM RZMZTYZ RcZ\\[',
	    'PoZIkI RcNlN RcRkR RYSaS RcVlV RcZlZ RWGW[ RZEZI R]S][ R_E`I RdLd[ ReEeI RhLhZ RZEkEkI R`QaT RYM]PZS R\\J[N R`UaY RXDSM R_L]P RZUYZ ReJbQ',
	    'Po[FbF RdFkF RZHbH RdHlH RhLkL R\\QjQ R\\TjT R\\WjW RWHW[ R\\N\\W R\\NjNjW R[DZG ReDdG ReYlZ RXDTM R]H[M RgCgHdM R^C_HaL^L R`YY[ RgEhL',
	    'PoYFcF RdIlI R[IaI RdNlN RYPbP RYRbR RdTlT RYUbU RdYlY RWGW[ RYLYP R[D[I RaDaI RYLbLbP ReGe[ RiGiY R^I]WaZ R]WY[ RXDSM RiDiK RfDcL',
	    'Po]LhL R[RjR R[TjT RYWlW RWHW[ RZGZJ R]I]L R`Y`[ RcCcG RcNcW R]IhIhL R[NjNjR RZGlGlJ RiXl[ R^D^G R\\YY[ RhDgG ReYe[ RXDTM R[N[R',
	    'Po[HaH RdHkH RZKlK R\\McM RePlP R\\SbS R\\VbV R[YbY RWGW[ R[D[H R\\P\\Y R_M_P R[DaDaH RZPcPb[ RdDdH RdDkDkH RgWl[ RhVd[ ReQiY RjQgY RXDSM RfLdT RZKZSX[',
	    'Po\\GiG R\\IiI RZMaM RdMkM RdPkP R[SdS R[VjV R_[c[ RVHV[ R\\D\\I R^K^O RZKaKaP RbDbI RcVc[ RdKdO RgKgO R\\DiDiI RdKkKkO RhTkW RfXlZ RXDSO RZKZPaP[S R_XYZ RgRZV',
	    'PoZEjE R]IhI R\\OaO RdOiO RZQjQ R[SjS RYUlU RVGV[ RYKYM R^W^[ R_P_U R\\L\\MaLaO RbCbJ RfPfU R]G]HhGhI RdLdMiLiO RYKlKlM RcUfYl[ RXCSN RjWfX RbU]XXY RcY]Z',
	    'Pod[k[ ReDe[ RWVR[ RlUk[ RYDYPWV',
	    'PoRFlF Rd[k[ RdFdZ RXVR[ RZFYQVX RlUk[ RZLXU',
	    'Po[LjL RWLfL RSLbL Rc[k[ RdNd[ ReGlN R\\CYJ RYVVYR[ RlVk[ R[NYW',
	    'PoVHjH RROlO R`[k[ RVHVO R`Q`Z RlWk[ R_C_N\\UXXR[ R_H^S',
	    'PoUFiF RRNlN Rc[k[ RcNcZ RlVk[ R[NYVVZR[',
	    'PoVOiO Rc[k[ RVEVO RcOc[ RVEiEiO RlVk[ R[OYVWYR[',
	    'PoRGlG Rc[k[ RcPc[ R[GXO R_C_G RlVk[ R[PZWWYR[ RUOhN RgLjP',
	    'Pob[k[ RcDcZ RSFWJ RiHeL RlXk[ RcOkS R[ORS R[D[PZUWYR[',
	    'PoUPiP Rc[k[ RUDUP RcPc[ RiDiP RbJeM RlWk[ RYFbJ RdD\\LXN RZVWYR[ R[PYX',
	    'PoWIjI RROlO R_C_O RXDTM R[PYWWYR[ RdPd[k[lW',
	    'PoRNlN Rc[k[ R_C_N RcNc[ RlWk[ RVEYK RhDeK R[NYVVYR[',
	    'PoTPhP Rb[k[ RcScZ RdLiQ RlWl[ R\\I[LWP R\\EdEhIlJ R[SYWWYR[ RYEUIRJ',
	    'PoRFlF RVRhR Rb[k[ RVKVR R_C_J RcRc[ RVKhKhR RlWk[ R\\RZWXYR[',
	    'PoVRhR Rb[k[ RVKVR RbRbZ RVKhKhR RlWk[ R\\EdDjK RYFSK RZVXYS[ R[RYX',
	    'Pob[k[ R`K`Q RcQc[ RlWl[ R[CSL RYFcF_J R[RYWWYR[ RVKjKjQVQVK',
	    'PoUQiQ Ra[k[ RUJUQ RaQaZ RUJiJiQ ReShW RlWk[ RiD_FTG R_F_N]TYYS[',
	    'Po`JjJ RUPlP Rc[k[ RVF[E]E`F`P RdPdZ RkWkZ R[VWZ R]P[X RVEUQTY RiC_E',
	    'Po\\KiK R\\RiR Rc[k[ RUDUR R\\E\\Q RcSc[ R\\EiEiQ RlWk[ R[SYWWYR[',
	    'PoUQhQ Rb[k[ RUIUP RcQcZ RUIhIhP RlWk[ RWD[G ReEaI RZVWYR[ R[QYX',
	    'PoUK]K RaJiJ RUQiQ Rc[k[ RUEUP RcQc[ RaEiEiP RlWk[ RZVWYR[ R[QYX R]DUE',
	    'PoUG^G RaGkG RRO^O R`OlO RZY^Y Rf[l[ RUGUO RZKZX R_U^X RaGaO RgLg[ RlWl[ RWVR[ RdU^[ RfObX RYPVX R[C[JXT RgCgIeT',
	    'PoURjR Ra[k[ RVIVQ RaRa[ RTKjKjQ RdThW RlWk[ R[DRL R]TYXS[ RYFcF_J]T',
	    'Po\\JbJ RUPiP Rb[k[ RUDUR RUD\\D\\K RbDbK RcPcZ RbDiDiQ RZVWYR[ R[PYX RlVk[',
	    'PoRFlF RUQhQ Rc[l[ R_C_F RcRcZ RlXl[ R^KYQ RcNhP RcIkL RZVXYR[ RZIRL RZSZX',
	    'PoRFlF RWSgS Rb[k[ RWLWS R_C_F RcScZ RVNgMgS RkWk[ RWMRO RYJTO R\\JdIhNlO R\\SZWXZR[',
	    'PoTE^E R`KkK RRK^K RaUjU R[[k[ R[K[Z RaNaW RjHkL RaNjNkWkZ RdCaK RVKVQTY',
	    'PoRG`G RaLlL R\\[k[ RYCYK RSKSQ]Q\\Z RSK^K^Q RfDfX RlWk[ RTZR[ RWQUX',
	    'PoWSgS Rb[l[ RSHSM RWLWS R_C_H RcSc[ RWLgLgS RSHkHkM RVDYG RlXk[ RhDeG R[SZWXZR[',
	    'PoRG_G R`MlM R[[k[ RYCYK RSKSQ]Q\\Z RSK^K^Q RgFgX RlWk[ RjEaG RVQUX',
	    'Po[KcK R[QcQ RTQYQ ReQjQ Rb[k[ RTFTR R[F[P R^C^F RcSc[ R[FcFcP ReFjFjR RlWk[ R[SYWWYR[ RYETG',
	    'PoRG_G ReWhW R[[k[ RYCYK RSKSQ\\Q\\Z RSK^K^Q RaMiMiV RhGlK RcS_W RlWk[ RgDiI RcD`K RdMcT RVQUX',
	    'PoRG^G ReVkV R[[k[ RYCYK RSKSQ\\Q[Z RSK^K^Q ReGeV RkSkV RlWk[ RiE`H RVQUY RjJ`L RjO`Q',
	    'PoTI]I R`IkI RRN^N R_NlN Rg[l[ RYCYN R[N[X ReCeN RhNh[ RlWl[ R_VZX R`Z^[ RUDSL RaD_L RVPTY RbNaX',
	    'PoRHWH R[LcL R[QcQ RgQlQ Rb[k[ RWDWR R[F[Q RcScZ R[FcFcQ RgDgQ RlNlQ RlWkZ R^C]F RkHfJ R[SYWWYR[ RWNRO',
	    'Po`EkE RRG^G RaPiP RaWiW R[[k[ RYCYK RSKSQ]Q\\Z RSK^K^Q RaJaV RaJjJjV RlWk[ ReEdJ RWQUY',
	    'PoRF^F R_FlF Rg[l[ RXCXJ RTJTQ[QZX RTJ]J]P ReCeJ RaJaQiQh[ RaJjJjP RbW^[ RlWl[ R_WYX RVQTY RcQbX',
	    'Po`DlD RRG^G RcKkK RcOkO RbSkS RbVlV R[[l[ RXCXK RSKSQ\\Q[Z RSK]K]Q RcGcO RgGgV RcGkGkO RVQUY R`D`P^W',
	    'Po]FlF R]IlI RbKhK RRM\\M R]MlM R_RjR R_UjU RY[k[ RYMYZ R_O_U RbGbK RdCdF ReMeU RhGhK R_OjOjU RgVkX RlWkZ RSETK RbV]X RZEYK RWCVMURSZ R]F]P\\W',
	    'PoXF_FaLdTk[ R_H^N[TT[',
	    'PoS[l[ RSESZ RcQiW R]PVW R`KdT R`I[R RYF_EcQ',
	    'Pof[l[ RRIR[ RRIlIl[ R_IbPeSiT R_H\\OYSUT RXE_EaO',
	    'PoVNhN RUTiT RS[k[ R_N_[ RVLRN R_CVL R_CgKlN',
	    'PoRElE RbNeN Rf[k[ RSJS[ R_E_[ RSJkJk[ ReOhW ReObW RZOVW RWNZN\\V RYNWN',
	    'PoYKeK RZ[][ RUNU[ RUN]N][ R`TaWc[ RfPeTgWi[ RWQ[R RWU[W RWE`DdHlL RhNfR RcN`T R^DZHRL',
	    'PoZKWSTZ RZFdFeNgShWl[',
	    'PocSjZ R]MWZ RZDXJRN RdDeHlO RUZhX',
	    'PoRJlJ R_C_J RYPVWR[ RdQhTk[',
	    'PoWKhK R^[d[ R\\EdElL RYFSL RZLWS RXRgQe[',
	    'PofUlU R]K][ RRKlKlU RVD[H RiCdH',
	    'PoSJkJ RURiR RR[l[ RWDZG RfDeGbJ',
	    'PoSJkJ RRRlR RYCYR ReCeR RgXj[ RcUfW RZUWYR[',
	    'PoTEjE RUJiJ RROkO R_E_N RdTk[ RYUR[ RdReV RYRWW',
	    'PoTJjJ RSPlP RWDZG RhDdH R_NaUeXl[ R_J_S[XR[',
	    'PoRQlQ RdTk[ RZTS[ RfLdO RTGWM R\\D_K RjEhK',
	    'PoWKkK RRTlT RWFWT ReKeT RcVl[ RZWR[ R^EWF RgD[E',
	    'PoSGkG RXLfL RXQfQ RRUlU RXCXU RfCfU RcXlZ RZXR[',
	    'PoVIhI RVMhM RVRhR RRUlU RVEVR RVEhEhR RZXR[ RcXkZ',
	    'PoUMiM RRUlU RUGUT R\\C\\T RcCcT RUGiGiT RcWk[ R[WS[',
	    'PoRIlI RaZkZ RSZ]Z RXDZG RSOXUSZ RgQeT R_OeU`Z RfEcG R[U^[ RiVl[ RhOgS RXJUQ R\\OZS RdJaQ',
	    'PoaPeP RTY\\Y Ra[k[ RTRT[ RTR\\R\\Y RaQa[ RkXk[ RVIfHlJ R]JZNUP RZCVHRJ RjRaU RcCgIeO',
	    'PoTGkG RUJiJ RRNlN R_G_N RcQc[ RYCZG RYWU[ RfDcG RcNeQlT R[NYQRT RZQYX',
	    'PoSGkG RSOkO RUThT R\\G\\[ RbGb[ RUKhKhT RXCZF RfCcG RbSfWlZ R\\SWXRZ',
	    'PoUKiK RUOiO RRRlR RVZhZ RUGUN RVUV[ R_G_N RVUhUh[ RUGiGiN RYD[E ReDbG',
	    'Po\\EdE RVLhL RVOhO RSRkR RRVlV RWGWO RZOZV R_H_O RdOdV RUIiHhO RcXk[ ReFkI R[XS[ RYEVHRI',
	    'PoRFkF RRJ\\J RTQVRgS RRVkV RXGXQ RiQlPlQ RTZ_ZgYi[ RdXhY RYD[F RbM^Q ReCbF R_LeO R\\VTZ RcHaN R^JhJiQ R\\MRN',
	    'PobJkJ RVNhN RVQhQ RSTkT RRWlW RVKVQ RZQZW R_K_Q RbCbI RdQdW RVKhKhQ RcXl[ R[XR[ RjDaF RSE\\D\\ITI',
	    'PoSL[L R_NkN RSQ[Q R_SlS RRV\\V RSGSQ RWGW[ RSG[G[Q RbSb[ RcIcR R_IjIiR R_DkDkI RTCUF RgWk[ RkUhW RfShY R[CYG RfZaZ R_D_Q]X',
	    'Poe[l[ RRER[ RRElEl[',
	    'PoWKgK RVRhR Rf[l[ RRER[ RRElEl[',
	    'PoRLlL RRRlR Ra[g[ RWEW[ RWEhEh[',
	    'Poe[l[ RRHR[ RRHlHl[ R_C_MbOdQiU R^NYSUU',
	    'PoROlO Rf[l[ RRER[ R_E_O RRElEl[',
	    'PoSGlG Rb[h[ RZKZ[ RXMhMh[ RZJSR R]CYM',
	    'Pof[l[ RRER[ RRElEl[ R_OfV R\\M_P RWJZL]M ReHdKZUVW',
	    'PoVMiM RRSlS Rc[h[ RVGV[ R_C_S RVGiGi[',
	    'PoROlO Rf[i[ RUEU[ R\\E\\[ RcEc[ RUEiEi[',
	    'PoYTeT Rf[l[ RRER[ RYKYU RYKeKeS RRElEl[',
	    'PoRNlN RX[\\[ Rd[h[ RVE\\E\\[ RaEiEi[ RVEUQTY RaEaQ_Y',
	    'PoSEkE RVOhO RRTlT Rc[h[ RVJV[ R_E_T RVJhJh[',
	    'Po^IhI Rf[l[ RROR[ RVEVO R^I^O RVEhEhO RROlOl[',
	    'PoYWeW Rf[l[ RRER[ RYOYX RYOeOeW RRElEl[ RZKUM RcEbLiKiL R[EZK',
	    'PoVHhH RVLhL RUSiS RUWiW Rc[i[ RRERM RUPU[ RUPiPi[ RRElElM',
	    'PoRIlI RRMlM RXSfS RXVfV Rc[i[ RRERM RUPU[ R_C_M RUPiPi[ RRElElM',
	    'PoRHlH RRLlL RUSiS RUViV RUZiZ RRDRL RUOU[ RUOiOi[ RRDlDlL',
	    'PoTEkE RUIiI RRLlL RVShS RRWlW Rb[h[ RVOV[ RZCZL R_L_W RdCdL RVOhOh[',
	    'PoXHdH RXKdK R\\OkO R[TlT RRWYW R_[c[ RRORY RVDVL RROYOYW RdOd[ RVDgDgL',
	    'PoXGfG RXJfJ RVUhU Ra[k[ RUDUK RVOVT RaUaZ RTPhPiT RUDiDiK RlWk[ RZKWORQ RYMcM_Q^VZYR[',
	    'PoRRR[ RRRlRl[',
	    'PoSESK RSEkEkK RYKYRXVS[ RYKeKe[l[lV',
	    'Po`[k[ RSISM R_C_M`Z RSIkIkM R^O[UWYR[ RlUkZ R_G]S',
	    'PoYKgK RRTlT R][c[ RSESI RSEkEkI RZFWPfOdZ',
	    'PoTYjY RTNTY R_I_Y RjNj[ RRFRJ RRElFlJ',
	    'PoUKhK RVQiQ RRVlV RRERI R`M`[ RRElElI R[GWQ',
	    'PoTGTK RYOY[ RTGjGjK R_KcU RhNfPbRgXlZ R`D\\LXPRR R^HaR R`XX[',
	    'PoXPfP RXUfU RR[l[ RSESJ RXJX[ RXJfJf[ RSEkEkJ',
	    'PoRPlP RSESI R_H_[ RSEkEkI RVIZM RgIcM R_OeVlY R_OXWRY',
	    'PoUMiM RURiR RV[j[ RRDRI RUIUR RVSV[ RUIiIiR RRDlDlI RkXj[ R^F]I R[VUW R`VYV RiT]V',
	    'PoTJ^J R`LlL RRO_O RdXhX R\\[k[ RSESH R\\O\\Z RhHhX RSEkEkH RkWk[ RbPdT RVQUX',
	    'PoVHgH RTLjL RRERI RREkEkI R`LcTgXlZ R]QSV RZO_T]VR[ RhQcS R_LYORQ R^R`W_[[[',
	    'PoVIhI RZ[_[ RRERI RREkEkI RVP[V R`KdShWlX R_PbVaZ R_ITN R]ORT RiLgOcP R[M`R]URZ R`IbQ',
	    'Po`KjK RUN]N RUR]R RRERH RUIUX RSI^I][ RREkEkH RdTjZ RgT`[ RaMeV RjLfV R\\WRX',
	    'PoaLdL RVShS Ra[k[ RWMWT RbSbZ RUNhMhS RSEkEkI ReUhX RlWk[ RVHeHdL R[HXMRN RSESI R]UYYR[ R_M]V',
	    'PoWMgM RWQgQ RRUlU RSESI RWIWQ R_R_U RWIgIgQ RSEkEkI RdWl[ RZXR[',
	    'PoUJ^J ReKkK RRO_O R\\[k[ RSESH R\\O\\Z ReHeN RSEkEkH RcRfTiW RlWk[ RaOjOhReU_X RVQUX',
	    'PoRM^M R_MlM RSDSI RYHY[ ReHe[ RSDkDkI RXM]S RhSlV RbR]W RUSRV ReKhS RYLTU ReKaT',
	    'PoVHhH RWOhO RTVkV RTZkZ RRERI RTRT[ RWKWO R_R_Z RWKhKhO RTRkRk[ RRElElI',
	    'PoVL]L RbLiL RVPiP Rd[i[ RSESI RVIVP R[V\\Z RaHiHiP RSElElI ReUgX R`VaY RWVUZ R[PYSRV R]GVH RWTkTj[',
	    'PoUHhH RVMhM RVPhP RRSlS RdZgZ RSESH RVJVP RXUXZ R[F[I R_T_[ RcFcI RVVhUgY RVJhJhP RSEkEkH ReTlW R\\PYTRW',
	    'PoXH[K RSFXH R[QR[',
	    'Po\\CTK RYH^MeQlR R[RdT RZGfG`MXQRS RWXgZ',
	    'PoRIlI R^PaS RVPRR R[KTR RbIcLhQlR R[O`Q R^CZM RgWWZ',
	    'PoZFkF RYZlZ RcFcZ RSEWI RVRR[',
	    'PoZUhU Rc[i[ RREWI RVQRY R]H\\OlOj[ R[EiEhP',
	    'PoYK_K R][b[ RbCb[ RSFVI RfRlX R\\SXW RjIeO RbIgT RVPSX R_L[U RbFeQ',
	    'PoYEkE RX[l[ RSEWI RWPSY R^LiLiRgX R^E\\UhTg[',
	    'Po[QlQ R[I[S RcCc[ R[IlIlS RSEWI RWPTV',
	    'PoYPlP R[HiHiP RSDWI RbCbPeVhYl[ RVQRZ R`U\\YX[ RbLaU',
	    'Po[EkE R[NlN Rb[g[ R_E_N RhEh[ RTEWI RWPVTRW RhN_VZY',
	    'Po\\NjN Re[l[ R\\D\\N RfNf[ R\\DjDjN RlWl[ RTEXI R^VZZ RWOSW R`O^X',
	    'Po[MjL R\\ZjZ R\\Q\\[ R\\QjQj[ RSEWI RfHkM RaC\\M RRXUUXP',
	    'Po]LgL ReXiX RaPa[ RZPiPiX RSEWI RbCiKlL RaDYL RWQUURX',
	    'Po\\MjM RZSlS RcCc[ RSEVI RfFkJ R^GZK RWORX',
	    'PoYIlI RSDWH ReDiG R^NaTeXl[ RVQRZ R^NZY R^NiNfTbX\\[ R_C_J]T',
	    'Po[JjJ R[QlQ R[D[Z R[DjDjJ RTEWI RlWk[ RgZj[ RVORX RcKeW RdYYZ',
	    'PoYGlG R[PkP R_[c[ RdKd[ RREVI RhTkW R]T[X RVQRZ R`E[P',
	    'Po\\IjI RZOlO Re[l[ RcCcO RfOfZ RlWl[ RSEWH R^VZZ RWPTW R]DZL R_O^X',
	    'PoYDeD Rg[l[ R]P`S RfGfT RlCl[ RSEWI RZO_Q R_UZ[ RVORX R]HYQ RbN^X R]EZO R\\KcK`U',
	    'Po]LhL R\\ZiZ R\\Q\\[ R\\QiQi[ RSEWI RbCiKlL RaDZL RUURX RWPUT',
	    'PoXJlJ RZOiO RYSjS RXWlW RbCb[ RZFiFiO RSEVI RVORX',
	    'PoXOlO RZTiT R][a[ RaJa[ RYJiJiU RREVI R^C]GXK R]FfFaK RVQRZ',
	    'PoZGkG R[QjQ R[K[Q RcCc[ R[KjKjQ RSDWH RcQkY RbQZY RVQRZ',
	    'Po[RjR Rc[k[ R[J[R RdRdZ RZKjKjR RSEWI RlWk[ R]GYL RVPRX R_C]I RaU]YX[ R^GfFdIcKaV',
	    'Po]LhL RZQkQ R^[b[ RcLc[ RSEWI RgTkX RcCjK R\\UYY RbCZL RWPTV',
	    'PoYFlF RYMlM R[PiP RYSlS RbCbP R[IiIiP RSFWI RVQTW R_P\\W RkZ RhSdXaYX[',
	    'Po^KiK R_UhU R[YlY R[E[[ R_P_U RcGcO R_PhPhU R[ElEl[ RSEWI RWOSW',
	    'Po\\IkI R]NkN R]TkT R]ZlZ R]G][ ReGeZ RTEXI RWRTW R_CZM ReCdK',
	    'PoXJ_J R\\D\\[ R]L_P RSEUI RURRY RhDkM R[HXS RdEaM ReLbZ RhTkZaZ',
	    'PoYPlP R[UiU R^[b[ RbLb[ R[LiLiV RSEWI RhFkI RbGcJ RVORX R\\EYK RbE\\E RhD_E',
	    'PoYGlG R\\QiQ R^[b[ R\\K\\Q RcCcG RcQc[ R\\KiKiQ RSEWI RhUlY R]UXY RWPTV',
	    'Po[ElE R\\HkH RZLlL R\\UjU Rf[j[ R\\O\\[ RcCcL RcOcU R\\OjOj[ RSEWI RWORY',
	    'Po^IiI R^MiM R_VhV Rg[l[ R_Q_X RcFcM R_QhQhV R[DlDl[ RSEWI RUTSX RWPUU R[D[QZX',
	    'Po[FjF RZJlJ RbCbJ ReJeOkNlO RSEWI RaOZV RVPRX R_J^NZP R^SbWgZl[ R^ShSdW`YY[',
	    'PoYFlF R[NjN R[SjS R[J[S RbCb[ R[JjJjS RSEVI RWPSW RbR]XXZ RcSiXlZ',
	    'Po]LhL R[RjR RY[l[ RbLb[ RREVI RbDhJlK R]T^Y RhTfY RVRRZ RcC\\JYK',
	    'PoZHkH R\\LcL R]UcU R]O]W R]OcOcU RSEVI RhCkF RgSaZ RjMfV RVORX RkXk[i[ ReCeMgY RZHZRXY',
	    'PoYElE RYPkP RZUkU RX[l[ RYIYP R_E_P RbRb[ ReEeP RYIkIkP RSEUJ RVRRZ',
	    'PoYFkF RZJjJ RXMkM RZUiU RSEVI ReMjR R_MYS RcJfO RaTcWeYj[ RVPRX RaH]P RaV^YY[ RaD_N RbQ`X ReP]Q',
	    'Po[IjI RZMlM R\\UiU RY[l[ R[D[I R\\P\\U RcCcI R\\PiPiU RjDjI RSEWI R^W_Z RgVeZ RVPRY',
	    'PoZEkE R[OkO RZSlS R[I[O R`E`O RcPc[ ReEeO R[IkIkO RSFWI RcRhXlY RVPRX RbR]XYZ',
	    'Po\\OiO R\\RiR R]ZiZ R\\L\\U][ R\\LiLiR R]UiUi[ RSEWI RcChH R^IfHlJ RWPSW RbC\\IXJ R\\O[X',
	    'Po\\LiL R\\PiP RYTlT RYEYH R\\I\\P RbPbT R\\IiIiP RYEkElH RiXl[ RSEWI RfWiX RVORX R^W\\YX[',
	    'Po[GjG R[JjJ R[MjM R[QkQ RRUlU R\\F\\Q R_R_[ RcEcQ R]DXI RXLRQ RSDXF',
	    'Po[IkI R[NlN R[RkR R[VkV R[ZlZ R[D[I R\\M\\[ RcCcI RdLdZ RkDkI RTEVJ RcJdN RVQRZ R\\JYR',
	    'PoXGbG RZK`K RZP`P RWTbT RZCZT R`C`T RdLkLj[ RSEVI R_VaY RZVYZ RVORX RkDdEdScZ',
	    'PoYIkI RYLjL RXPlP RYTkT RWWlW RYDYI R_D_I RbIbO RbTb[ ReDeI RYDkDkI RfQeT RTEVJ R]Q^T RVQRY',
	    'PoZGbG RcGlG R[PjP RYSlS R^[b[ R^C^N RbSb[ RgCgN RhVkZ R]GaK RhHlL RgEiJ RfHbM R\\VXZ R\\IYM RSDVJ RVQRY R^E[K RgEeJ',
	    'PoYFlF R_MfM R[PjP R\\RiR RYUlU R^[b[ R[H[O R_K_M RbCbF RbUb[ R_KfKfM R[HjHjO RgWjY RSEVI RWOSX R\\XXZ',
	    'PoYFlF R^MfM R[PjP RXUlU R[H[O R^K_M RbCbF RZScRb[ R^KfKfM R[HjHjO RSEVI RcUeXlZ RWOSX RbT`WXZ RiQbR',
	    'PoYJ`J RYO`O RgSlS RXSaS RZCZJ RbMkMkP RSEVI RbEkDgI RcHiJ RVQRY RZLYQ R_EYG RcPbY R]O\\U[XX[ R\\U_X RcVgZm[ RgMgZ',
	    'Po[EcE ReLkL R[NbN R[ScS ReSlS R[E[S R_N_S R[IbIbN RfDfS ReDkDkK RlPkS RSDUG RhVl[ RUXR[ R[W\\[ RaVc[ RVLRS R_E_I RXDXNWT',
	    'PoYEkE R[HjH ReLkL RZNkN R\\SjS R\\UjU R\\WjW RZJZN R\\P\\W RcCcG ReJeL R\\PjPjW RZJkJkN RSEWI RfYl[ RWORX R`K\\M R`YX[',
	    'Poe[k[ RXEeEeZ RWVR[ RXEXRUY RlVkZ RXMWV',
	    'Poe[k[ RXEeEfZ R[LaQ RXEXRTZ RlVl[',
	    'Poe[l[ RXHeHeZ RVWR[ R]C\\H RlVk[ RXHXQWW',
	    'Poe[k[ R_C_H RYHeHeZ RWVR[ RlVk[ RYHXQWV',
	    'Po\\PdX RZO\\O RYJdJaR[WWY RlWl[ RVEhEiSj[ RVEUPTY',
	    'PoVJjJ Rf[l[ R[NfNfZ RVEjEjJ RlVl[ RZWV[ RVEVPTX R[N[SZX',
	    'PoaEgEgVjVkU RWCTP RaEaN_U RUKWQYU\\X`Zg[m[ RVI]I[QXVS[',
	    'PoaWeW RYKYW R_G_Z RYKeKeV RlWl[ RVEiEiTj[ RVEVPTY',
	    'PoZJeJ RYOeO RlWl[ R]OYY RUEiEiTj[ RUEUPTY RaXYY RbRdX\\X',
	    'PoXMfM R_G_Z RcSgW R\\SWW R_KcU RlWl[ R_K[T RVEiEiTj[ RVEVPTY',
	    'Po_OeO RVYgY RZLZY R_H_Y RlWl[ RVEiEiTj[ RVEVPTY',
	    'PofUjU Rc[l[ RYRcRcZ RlXl[ R^D\\F R[IaJ RYVVYR[ RVFgFgKcK RVFVNkNjU RYRXX',
	    'PoXIfI RZRdR RZWdW RZMZY RZMdMdX RlWl[ RVEhEiSj[ RVEVPTY',
	    'PoYJkJ R[OkO Re[l[ RWGWP RcEcO RYReRfZ RlWl[ RYDRK RXYS[ RYRXX RiD^E',
	    'PoXIfI RYSdS RYXdX RYNYY R^I^N RYNdNdY RlWk[ RVEhEhTi[ RVEVQTY',
	    'PoRJ]J RSS]S Rh[l[ RRERJ RSSSZ RXCXJ RRN]N]S R]E]J RaEhEi[ RlXl[ R\\XRY RaEaQ`Y',
	    'PoYLeL RYOeO RXRfR RYVeV RVZhZ RYIYO R_R_Z RYIeIeO R^F_I RlWl[ RVEiEiTj[ RVEUPTY',
	    'PoSI^I RRL_L RTS]S Ri[l[ RSESI RTOTS RXDXI RTO]O]S R^E^I RaEiEiZ RlVl[ RVUVX R[UZX RaEaQ`W R[YRZ R]YUZ',
	    'PoWKgK RWQgQ RXTdT R]G][ RaHa[ RXMeMeT R`TfZ R]TVZ R^GXH RdG_H RUEiEiTj[ RlWl[ RUEUPTY',
	    'PoXNfN RRRlR Rd[l[ RXKXN RYUeUe[ RXKfKfN RlXl[ RUGXI RZO[R RaCfI RdObQ RZIdHlK RgDcF RjFeH RXUWYR[ RTD\\DYHRK',
	    'Po\\EjE R\\HiH R\\JiJ RfTjT R^P^S RbEbM RXUeUe[lYl[ RaPcR ReOgQ RXKRQ RZPXS RTDXF RXUWYR[ R\\E\\MkNjT',
	    'PoRXlX RRFR[ RlFl[',
	    'PoRYlY RRFR[ RlFl[ RfEbKXV RVHdPhT',
	    'PoWKhK RUTiT RRYlY RRER[ R_D_T RlEl[',
	    'PoRNYN RRYlY RRNR[ RYEYN RYEeEeNlNl[',
	    'Po[PcP RRYlY RRFR[ RRF[F[P RcFcP RcFlFl[',
	    'PoSNkN RRYlY RRQR[ RSFSN R_C_Y RkFkN RlQl[',
	    'PoTHjH RRNlN RUYiY RURUY R_C_Y RiRi[',
	    'Po[V_V RRYlY RRERY R_D_V RlEl[ R_JhR RfKbL RhHeK R[NUT RUJ\\JYQ',
	    'PoRElE R[W_W RSYkY RSJS[ RkJk[ RWK[O RbRfShV RgKdO R\\RUU R\\E[K`J`S_W',
	    'PobJiJ RVS[S RZW_W RRYlY RRIR[ RVJVU RVJ[J[S R_H_W RlIl[ RbMiU RfRbV RhLdU RTEhEdG_H',
	    'PoRJlJ RWPfP RXSfS RUYiY RULUY R\\C\\J R_P_W RbCbJ RiLi[ RVEXH RZL\\N RhEfG RcL_P',
	    'Poc[j[ RSFlFlQjZ R^G\\PZVWYR[',
	    'Poa[i[ RRFlFlQj[ RfORS',
	    'Pod[l[ R]F]T RlDl[',
	    'Poa[i[ RSFlFlRj[ R_G^P[UXYR[ RWNcS',
	    'Poa[f[ R\\OYVUZR[ RVJ\\RdVlX R]G[S RSFiFiMiPgZ',
	    'Po^[d[ RZUTZ RiKlS RVLSS R\\OXW R]G[S RSFfFfTe[',
	    'Po_[e[ RVNgNgSf[ RZDXHSN RdDeGlN R]N[VXYT[',
	    'PoVV\\V Re[j[ RWDWV R]R]V R^JRL R]FlFlSjZ RdFdLbR`W[[',
	    'Pof[l[ ReHeT RlCl[ RSG\\Q_T`W RaE[QRZ',
	    'PoUHaH RUZaZ Rg[l[ ReGeT RlDl[ RaVaZ RVGRK RUM`MUX RWCTJ',
	    'PoSE`E RRNaN Rf[l[ RZEZ[ ReGeT RlDl[',
	    'PoRNbN Rf[l[ RdGdT RlDl[ RYUT[ R`DRG RZFZPYV',
	    'PoRJbJ RX[][ Rf[l[ R]D][ ReFeT RlDl[ RUNXT',
	    'PoVSlS RTYlY RVMlMl[ RZGiHcM RVKRM R]CUL',
	    'Po[[`[ Rg[l[ RgFgT RlDl[ RZUVY RWQSU RXDSN RZIUS R]NYX R_H[U RWHbGbR`[',
	    'PoUGaG RVJ_J RSO^O R`YbZ Rg[l[ RcWb[ RfGfT RlDl[ RVCSK R^P_Y',
	    'PoROaO Rf[l[ ReGeU RlDl[ RYHZL RVE_E_[[[ RVEVRTY',
	    'PoSEaE RRNbN Rf[l[ R^E^[ RfFfT RlDl[ RWEVQUX',
	    'PoXK^K Rg[l[ RfFfT RlDl[ R\\W_[ R[D_H RbWa[ RYQ]Z R_M^RWXRZ RRLXL\\X R[KUK RWEZU RbJ\\K',
	    'PoTE`E RRLbL Rf[l[ R]L]W RdGdT RlDl[ RbUZW RWMUX',
	    'PoRPdP Rg[l[ RTH`H`O ReGeT RlDl[ RZCZRaY RXUS[ RZLXV',
	    'PocNgN RaPlP RX[\\[ Rf[k[ RWWR[ RdW`[ R_E]IZLUN RXPVX RfPdX RWEiEhM RlRk[ RSP^P][',
	    'PoUK_K RUQ_Q RZ[_[ Rg[l[ RUE_E_[ RfGfU RlDl[ RUEURTY',
	    'PoSEbE Rg[l[ RdFdT RlDl[ RUO]S RYEUNRQ RVL`L\\TV[',
	    'PoRHbH Rg[l[ RZCZH RdGdT RlDl[ RTM^V R[T_W RZTS[ R]LXV R]JZT',
	    'Pof[l[ RTETT RTE`E`T RfFfU RlCl[ R\\W`Z RYUT[ RZHZQYV',
	    'Po\\[a[ Rg[l[ RRER[ RREaEa[ RfFfT RlDl[ RWTTX RUJXLZO]V R^GWV',
	    'PoZU^U RV[`[ Rf[l[ RVMV[ RfGfU RlDl[ R[DbL RaWa[ RZCYGRM RUM_N^U',
	    'PoSMbM Rg[l[ R]C][ RSGbGbL RfGfT RlDl[ RWWR[ RbX_X RXCXRWW RSLRQcQbX',
	    'PoXCXH RXOX[ RXO\\S R\\NZP RRI\\IZMST RdGdLbR`W\\[ R^GlGlTj[e[',
	    'PoUK_K RUR_R Rf[l[ RUEUY ReFeT RlDl[ R\\YRZ RUE_E`XWY',
	    'PoSEaE R[N`N RTY`Y Rg[l[ RTQT[ RTQ`Q`[ ReFeT RlDl[ RXJSN RZEVL RaF`N',
	    'PoRNeN RV[Y[ R_[c[ Ri[l[ RTDYDY[ R]DcDc[ RgGgU RlDl[ RTDTTSZ R]D]T\\[',
	    'PoSJ`J RSPaP Rf[l[ RSDS[ RSD`D`J ReFeT RlDl[ R[S^Z RZYRZ RaXa[_Z RZK\\W',
	    'PoWK`K R]WaW Rh[l[ RYOY[ RTOaOaW RfGfT RlDl[ R[CbJ R[CTJ',
	    'PoXMaM RRM[M RTZ`Z Rf[l[ RTQT[ RTQ`Q`[ ReFeT RlDl[ R\\HaM RVIRM RXDVJ',
	    'PoSLaL RRRbR Rf[l[ RZCZ[ RdFdT RlDl[ RTEVJ R`E^J',
	    'PoTL_L RY[^[ Rf[l[ RTDTL RTD_D_L RdFdT RlDl[ RXLUX RWQ_Q^[',
	    'PoTHaH RRObO RRXZX Rh[l[ ReGeT RlCl[ R]SaX RZCYOVX R^WYXUX R`WYX',
	    'PoSH_H RRN`N Re[j[ RaW][ RcQ_Z RYDYMVQTW R`FlFlUk[ RdFdLbV R]S_VSW',
	    'PoUQ\\Q R\\T`T RU[b[ Rh[l[ RVKVZ RUK\\K\\Q RcWb[ RfFfU RlCl[ RWCTK RVGaGaO`T',
	    'PoSLaL Rg[l[ R[E[[ RdGdT RlDl[ R_DRF RZLVRRV R[O`S',
	    'PoRNdN Rg[l[ R_[b[ RTET[ RYEYZ R]E]Z RTEbEb[ RfGfT RlDl[',
	    'PoTL_L RY[^[ Rf[l[ RTDTL RTD_D_L ReFeT RlDl[ RWWS[ RYNWX RRQ_Q^[',
	    'Pog[l[ RfGfU RlDl[ RYQ]Z`[ R[E_F RbWa[ R_R[VRZ RXCXIZV R]ITJ R`IVJ RaNRP',
	    'PoSQaQ Rg[l[ RfFfU RlCl[ RSF`EZKbN R\\JRN RZQ[YSZ R`XXY',
	    'PoRKbK RTZaZ Rg[l[ RTRT[ R[E[R RTRaRa[ RfFfT RlDl[ RaDRF',
	    'PoSFbF RTP`P RTV`V Rh[l[ RTJTV RTJ`J`V RgFgU RlDl[ R\\ZR[ R`YWZ RbYYZ',
	    'PoREbE RRM`L RSRaR Rf[l[ RdGdT RlDl[ R]IbN RXEUM RaXTZ RZNZY',
	    'PoRI^I RRQ^Q Rc[g[ R[C[[ RbR_Z RkKlQ R`J^P RVCUSTY RdEdLbV R_EiEiTg[',
	    'PoSG`G RRMaM RSS`S Rg[l[ RZCZM RdGdT RlCl[ RZN[YSZ R_YVZ RaXYY',
	    'PoSGcG RWM_M RSPbP RZ[^[ Rg[l[ RfFfT RlDl[ R\\GaL RZGTM RZCXJ RXPVU`T_[',
	    'Po\\N`N RSRbR Rg[l[ RZNZ[ RfGfU RlCl[ R]TaX RZRSY RYS_U RUMRN RXEVM RSE]E\\IaI`N',
	    'PoREaE RUJ^J RUP^P Rg[l[ RUEUV R^E^[ RdGdT RlDl[ R]URW',
	    'PoTHaH RRLcL R]YaY Rg[l[ RTQTY RZCZ[ RTQaQaY RgFgT RlCl[ RVDSK',
	    'PoUJbJ R^XbX Rg[l[ RWOWX R\\J\\[ RUEbEbJ RWObObX RfFfT RlDl[ RUEURSZ',
	    'PoTIjI RRNlN R_[d[ RXDYH RWQRS R[LTS RgEdH RbIcMhRlS R_DZN R]R\\WYYU[ RWRfRe[',
	    'PoSPaP Rg[l[ RZLZ[ RdFdT RlDl[ RXRRY RTE`K RZS`U RZNXT R^DXJRM',
	    'PoSGbG R]QaQ Rg[l[ RTKTQ RZCZ[ RTKaKaQ ReGeT RlDl[ RYRRY RZS^TaV RZOXT',
	    'PoRGbG Rg[l[ RZCZG ReFeU RlDl[ RUMXQ RSMWN R]Va[ RYHTM R^KWRRT RaPZWR[',
	    'PoSH^H RRN_N Rd[h[ R_JbQ RdFcOhVlW RbR_Z RXDWNTW R_FjFjTh[ RcIaV R\\S^WRW',
	    'PoVLaL RSQcQ Rg[l[ RgFgT RlDl[ R[D^GcL R[DTK RYQUZ RZYTZ R\\YWZ R^U`WaZ[Y',
	    'PoSHbH RSPbP Rg[l[ RfGfU RlCl[ RZQaY RYUSZ RUJWM R`J^N R[C[MYU',
	    'PoTJaJ Rg[l[ RTETJ R[C[J RaEaJ RfFfT RlDl[ RWS[U RXNRS RYJVQ RWO`O^TZXR[',
	    'PoRIaI RSR`R Rg[l[ RSRSZ RZCZI RRM`M`R RfGfU RlDl[ RaEaI RRERI RaWRZ',
	    'Po^LbL RSPbP Rg[l[ RZMZ[ RWE^E_L RfGfU RlCl[ RZPaW RYSRY RZOXT RULSM RWEVK',
	    'PoSGcG Rg[l[ R_P_[ RfGfS RlCl[ RZC[G RVWS[ RVIbO R_H[LWNRO RWQVX',
	    'PoTMbM Rg[l[ R[G[[ RSGbGbL ReFeT RlDl[ R_C]G RZQSX RVDWG RaX]X RTLSQbQaX',
	    'PoRDbD RSQaQ RZYbY RVY]Y RRZZZ Rf[l[ RZQZY ReFeT RlDl[ RXK[N RSJUO R]KaO RZFXK RUFSK R_G^K',
	    'PoREbE RTP`P Rg[l[ RTJTP RTJ`J`P ReGeT RlDl[ RVSWW RYYRZ R^S[YTZ R_YXY RbX[Y',
	    'PoSK^K RSP^P Rg[l[ RSFSZ RXCYF RSF^F^P ReFeT RlCl[ R\\U^XRZ',
	    'PoTJ_J RTO_O RTT_T Rf[l[ RTDTT RTD_D_T RdFdT RlDl[ R\\W`Z RVWR[',
	    'PoRI_I RRQ`Q RRU`U R[[_[ Rf[l[ RRMR[ RRERI RRE_E_I RRM`M_[ RdGdU RlDl[',
	    'PoTScS R\\ZdZ RWZ`Z RSZ\\Z Rg[l[ R\\C\\Z RgGgU RlCl[ RVIZP RaIdP RWHSP RaF^P',
	    'PoSO_O RST_T RZ[_[ Rf[l[ RSKS[ RYCYK RSK_K_[ RdFdT RlDl[ RSDTI R_D]I',
	    'PoRG`G ReViV R\\[k[ RYCYK RTKTQ]Q\\Z RTK_K_Q RcFcR RiDiV RlWkZ RWQVX',
	    'PoRGbG RTQaQ Rg[l[ RTKTP R[C[[ RTKaKaP RfFfT RlDl[ R]SaW RZQ^T R[QRY',
	    'PoRHlH RTP^P RTU^U RY[][ Re[j[ RTLT[ RTL^L^[ RcLcV RjKj[ RWCZG RfCdH',
	    'PoSPbP R^YbY Rg[l[ RZMZ[ R_R_Y ReFeT RlDl[ R^MaN RVVSY RVFaK RTF^H R_D[HSL RVRVW',
	    'PoSI_I RRP_P Rc[h[ R[I[[ RTCVG RaJdQhVlW R]C[G RcR_Z RVIVRTY R_EjEjTh[ RdEdLbV',
	    'PoTJ`J R][b[ Rg[l[ RRNR[ RTDTJ RTD`D`J RRNbNb[ RgGgU RlCl[ RZQ_V RZRUW RZKYS',
	    'PoVL`L Rf[l[ RgGgU RlDl[ RZE^FcK R[DSL RUPVU RaO]X RZO[T RbWTZ',
	    'PoSGaG RRLbL RUU`U RUZ`Z Rf[l[ RUPU[ RWCWL R]C]L RUP`P`[ ReFeU RlDl[',
	    'PoRH_H RgPkP Rc[i[ RYDYQ RYJ]L RXH[K ReKaP RYHRO RcC`J RgFdN R^U[YR[ RSSjSi[ RbGlGkP R]DRE',
	    'PoTIaI RTMaM R\\[`[ Rg[l[ RTDTM RTDaDbM RgFgT RlDl[ R\\VXZ RWORT RYSTY RZQXV R^Q[Y RWMURbQa[',
	    'PoSHXH R]HcH RSMXM R]McM R]SdS Rh[l[ R]D][ RfGfU RlDl[ RWWS[ RXRRT RXDXRWX',
	    'PoSGbG RRNcN RTY`Y Rg[l[ RTRT[ RZD[G RTR`R`[ ReGeT RlDl[ RVHWM R_H]M',
	    'PoYGbG RTH]H RZScS RXSaS RUS^S RRS[S Rg[l[ RfFfT RlDl[ RcXc[ RXDZJ]MaN RWPZW]Ya[ RcLbN R[O`P R\\D`E R`I[KSN R`T^VYYR[',
	    'PoSGcG RVL_L RVP_P RRTcT Rh[l[ RVDVT R_D_T ReGeT RlDl[ R^VbZ RWVR[',
	    'PoRQ]Q RSI[IZ[ RTDUG R[DYI RbR^Z RkKkR R_J^O RgZcZ RUIURTX RdEcLaV R^EiEiQgZ',
	    'PoRFcF RTMaM RRSdS RSWaW Rg[l[ RV[Z[ RTITM R[C[[ RTIaIaM RSPaPaX RfGfT RlDl[',
	    'PoTL_L RVU^U R\\[a[ Rg[l[ RRER[ RVOVX RVGVK RYLYU R^O^W RREaEa[ ReFeT RlDl[ R]G[K',
	    'Po][c[ Rh[l[ RSGSJ R[D[G R]L][ RcXc[ RSGcGcK RfGfT RlDl[ RURXU RXVS[ RZQVY RVJSR RUMZMXW R]LbLbU_U',
	    'PoUJ_J RRMcM Rg[l[ RV[[[ R[M[[ ReFeT RlDl[ RTOWR RaO^S R]UbW RXURX RVCTK RVE`E_M',
	    'PoSFbF RRNcN RTW[W Rg[l[ R[[`[ RTQTX RTQ[Q[V R`N`[ RfFfT RlDl[ RYHaK RZDYIWKRL',
	    'Pog[l[ RfGfT RlDl[ RVHYL R_TcX RUTYX R_HcL RWVR[ R\\SaU\\[ RTHXISN RbH\\N RSEYEVL RSQYQVY R\\EcE`K R[QcP_X',
	    'PoSGbG RTQ`Q RV[Z[ Rg[l[ RTKTP RZCZG RZQZ[ RTK`KaP ReGeT RlDl[ RUTSY R_TaX',
	    'Pog[l[ RfGfU RlDl[ RaQ^T RaF^I RZJaM RZUaY RVFTI RUQTU RZJWMSN RZUWYR[ R[CYK R[NYW',
	    'PoSEbE Rg[l[ R^O^Y RfGfU RlCl[ R]OcU R[LVPXQZV RXPRR RbKWVRX R\\EXJRL RZJ\\N^T R\\M^V R\\[W[',
	    'PoVJ`J RUSaS Rg[l[ RUNUS RUNaNaS RfFfT RlDl[ R^WaZ R[J[U^W R[D^EbI R[CSK RZUWYS[',
	    'PoSFcF RVU`U Rg[l[ R[C[F RVQ`P`[ ReFeT RlDl[ RVFYJaM R`F]JXLRN RVNVTUY',
	    'PoUI_I RRMdM RV[[[ Rg[l[ R[M[[ RTD`D`M RfGfU RlDl[ R_UbX RTOWR RaO]T R[S`U R[STWRX',
	    'PoSGcG RUQaQ RUUaU R[[c[ Rg[l[ RULUW R[K[[ RTMbMbU RcXc[ RfFfT RlDl[ R^HdM RXHRN RZCVK',
	    'PoUJaJ RUOcO RWZaZ Rh[l[ RWSW[ R\\J\\S RUEaEaJ RWSaSb[ RgGgT RlDl[ RUEUPSY',
	    'PoSFaF RTI`I RSMaM RRPbP RVZ`Z Rg[l[ RWSW[ RZCZO RUU`Ua[ RfGfU RlCl[ RWSRX RXPUU',
	    'PoRIdI Rg[l[ RSE[E\\[ R_J_RdQdR RgFgT RlDl[ R[RTY R[S_UbV RcM^N RSMXLXQRQ RbDXE',
	    'PoRFlF Rb[h[ RUIUQ RbIbO RXCZE RfDcF RUI]I]Q[Q RjHiQfQ R^U[YS[ RWK[L RWN[O RSTjTi[',
	    'PoTFcF RVKcK RYO`O Rg[l[ RYHYO R`H`O RgGgU RlCl[ R\\C\\F RWU_X RWRaS\\XU[ RTFTSS[',
	    'PoTJbJ RRMcM Rh[l[ R`[c[ RTQT[ RTETJ RYQYZ R[C[J R^Q^Z RbEbJ RTQcQc[ RfFfU RlDl[ RZMXT',
	    'PoTJbJ RUMbM RWRaR RVVbV R[ZbZ RV[^[ Rg[l[ R\\R\\Z RTEbEbJ RfGfU RlDl[ R_ObS R[MWR RTETSR[',
	    'PoZH`H RWW]W R_[b[ Rh[l[ RRNR[ RWRWX RZHZN RWR]R]W RTD`D`N RTDSNbNb[ RfFfT RlDl[',
	    'PoREcE RTM`M RSUbU RSYbY Rg[l[ RSPS[ RTHTM R[P[Y RTH`H`M RSPbPb[ RfGfU RlDl[',
	    'PoSIbI RRMcM RSRbR Rg[l[ RVIVR R[E[[ R`I`R ReGeT RlDl[ R^UaX RZR]U R[RSY RaCUE',
	    'PoUJ^J RTS^S Rd[h[ RTMTS RTM^M^S RYJYT^X RZC_H RXWT[ RXDRJ RaJeS RdEdQiVlX RYSWY RcQ_Z RdLbW R`EjEjSh[ RYOXW',
	    'PoTJ`J RTM`M RRPbP RTZ`Z Rg[l[ RRFRI RTST[ RZCZF RZHZP RTS`S`[ RRFbFbI RfFfT RlDl[',
	    'PoSFcF RWO_O RVY`Y Rg[l[ RVSV[ RWCWI R^C^I RVS`S`[ ReGeT RlDl[ R[J^KbO RVNRP RZIUO',
	    'PoSIaI RRLbL RTT`T Rg[l[ RSDSH RTOTS RZCZH RTO`O`S RaDaH ReFeT RlDl[ RVUWY R]U]X RYZR[ R[ZUZ RbY[Z',
	    'PoXH_H RUNaN RUQaQ RWZaZ Rf[l[ RUKVUW[ RUKaKaQ RWTaTa[ ReGeU RlDl[ R[D^FbI RVIRJ RZCTI RVNTX',
	    'PoUI_I RRLcL RX[\\[ Rg[l[ RfFfU RlDl[ RYNbX RWCUI RZMSQ R[QSU RbP\\SZVRZ RWF`E^L R\\Q]W][',
	    'PoSFdF RTLdL RWQcQ RXUbU RYZcZ RWZaZ Rg[l[ R\\C\\F R]N]Z RfFfT RlDl[ RXG`K RXNVT R`GWK RTLTTSZ',
	    'PoSFcF RTLaL RTOaO RRRbR RRUdU Rh[l[ RZ[^[ RTITO R[C[R R^S^[ RTIaIaO RfFfT RlDl[ RVVXY',
	    'Poh[l[ RSDYDYL R\\DbDbL RfFfT RlDl[ RWITL R_J\\L R[NaQ RZMWPRS R\\QUT R_TVX RWGSH R`F\\H RbV\\YT[',
	    'PoSF]F RbGlG RRI^I RUPjP Rb[g[ RYCYN RYITM RcCbHfLlN RYJ^L RiHeL`N R_V\\YW[ RWTiTg[ RUPTY',
	    'PoTIcI R]PbP RTPZP R]TbT RTUZU R_[b[ Rh[l[ R\\D\\I R]KbKb[ RgFgT RlDl[ RTETI RbEbI RTKZKZ[W[ RTKTSSZ R]K]U\\[',
	    'PoTGaG Rf[l[ RgGgU RlDl[ R\\LbKcM R^DaH RYCUG RUIRL R^H]L R[McQ R[MSP R\\PTS R_RUW RbU\\YT[ RXJYLRL',
	    'PoREdE RSMcM RTPbP RRTdT Rg[l[ RW[[[ RSHSM RXEXM R[T[[ R]E]M RSHcHcM RgFgT RlDl[ R`VcZ RVVRZ',
	    'PoREbE RWM`M R]VbV RUVZV RRZbZ Rg[l[ RRERZ RUPUV RWHWM RUPZPZV R]P]V RWH`H`M R]PbPbV RfFfT RlDl[',
	    'PoTNaN RTQaQ RSTcT Rg[l[ RTJTQ R[J[[ RTJaJaQ RfGfU RlDl[ RSFVI RYF[I R_FbI RVCSF R[CYF RaC_F RYTbX R[SWXRY',
	    'PoXN\\N R]SbS RWY^Y R_[b[ Rh[l[ RSOS[ RWUWZ R\\I\\N RSObOb[ RfGfT RlDl[ RSIcI`L R]O]S RYOXSUS RUEaE[G R\\IRM RXFaI RWU^U^Y',
	    'PoUHcH RUKbK RUObO RUScS Rg[l[ RVFVT R]G]S RgGgU RlDl[ R\\C]F RXVY[ R]V_Z RbVc[ RWCTI RTVS[',
	    'PoTDeD RVK^K RWT]T Ri[l[ RWNWV R]N]U R`LeKeP RgGgT RlDl[ RWGXJ RbKbTe[ R\\F[J RbR^Z RWZU[ R`G_P RbNcX RTDTSSZ RZKZSYX RaN`W',
	    'PoRHdH RTKaK RTMbM RRPcP RUUaU RUWaW RXZdZ RUZaZ RRZ^Z Rg[l[ RURUW R[C[P R[R[W RURaRaW RTEaEaK RfGfT RlDl[',
	    'PoUG[G R]GeG RWP`P RVZaZ Rg[l[ RVTV[ RVTaTa[ RfGfT RlDl[ R`McQ R_FbI RWFXJ R[K^L`M RVCSJ R^C\\I R[KVOSQ',
	    'PoTIcI RUU_U RWY_Y RTY\\Y R^[b[ RTETI RUQUU RZNZY R^E^I RUQ_Q_U RTEcEcI RfGfT R]V_Y RYEXIVMRP RlDl[h[ RUMcMcVb[',
	    'PoUFdF RWJdJ RVPeP RWScS Rh[l[ R[I[[ R_I_[ RWMcMcS RhGhU RlCl[ R^SdY R\\C]F R[SUY R`G_K RUFTSSZ',
	    'Po[EcE RXL`L R[NcN RVPcP Rg[l[ RY[][ R\\C\\N R^V^[ RUHcHcK RfGfU RlDl[ R[R^VdX R^PWS R\\TVW RaR]WUZ RUHUSS[',
	    'Po`FkF RTI]I R_JlJ R_NlN RUP]P Rc[i[ RTDULUQ RTD]D]I RUK]K]P ReCeF ReJeR RbGbI RhGhI R^U[YS[ RTSjSi[ RTGSP',
	    'PoWQ`Q RTTbT R\\ZcZ RYZbZ RWZ`Z RSZ]Z Rg[l[ RTETL R[Q[Z RfFfT RlDl[ R`V^Y RWVWY R[LcP RZLXORQ R_E]J RXGZJRK RbK_K RZDTE R\\DcEbL',
	    'PoWI`I RTQbQ RUWaW RUZaZ RTLTQ RUTU[ R[L[Q RUTaTa[ RTLbLbQ RfFfT R[D_EcI RWMXP R_M^P RZCVHRJ RlDl[h[',
	    'PoVI`I RSPYP R\\PcP RRScS Rg[l[ RSKSP RSKYKYP R[Q[[ R\\K\\P RVD`D`I R\\KcKcP RgFgT RlDl[ R[TbX RZSVWRX RVDVI',
	    'Po\\FcF RSIeI RTNeN RVQ]Q Rh[l[ RWDWI R\\C\\I ReXe[ RgFgT RlDl[ R`Xc[ RZQ[VVZ RaU[[ RaKcL RWSUW RcP`W R^L`Y RTNTTS[',
	    'PoWI`I RURZR R]RbR Rg[l[ RUMUR RUMZMZR R]M]R R]MbMbR RfGfT RlDl[ R^Vc[ RWVYY R[D_EcI RWWR[ R^VZ[ R[CTJ RWTUY R_T\\Y',
	    'PoVJ]J RTRXR R[R_R Re[j[ RTMTR RTMXMXR R[M[R R[M_M_R R\\V_X RUVXX RYE]F_I RZCTI RVTSZ R\\TZ[ RaFlFlSj[ RfFfMcX',
	    'PoUO_O RSVaV RSZaZ Rf[l[ RRHRK RSRS[ RZRZZ RSRaRa[ RRHbHbK RfFfT RlDl[ RTDVF R`D^F RZDZH RUK_K_O RUKUO',
	    'PoRFdF RTKcK RUMbM RUObO RURdR RSUbU Rh[l[ RVIVS RXCXG R\\I\\R R_C_G RgGgU RlDl[ RUIRM RUWc[ RaV\\YT[ R]YR[',
	    'PoRFdF RUWaW Rg[l[ RUPUX R[C[F R[J[O R^J^N RURaQa[ RfGfT RlDl[ RaLdN RUJSO RcI^J RRJXJWNUN RcM]N',
	    'PoVI^I RTRXR R[R`R Rf[j[ RTMTR RTMXMXR R[M[R R[M`M`R R\\V_Y RVVXY RZC`I RZCRJ RfRbZ RVTS[ R]TZZ RcKbR RaElElUk[ RgEgMeV',
	    'PoTH`H RTJ`J RTL`L RSQbQ RSSbS RRVcV Rg[l[ RSNSS RTETL RZNZS R^V^[ RTE`E`L RSNbNbS RfGfT RlCl[ RWVUZR[',
	    'PoVI]I RSRXR RZR_R Rd[h[ RSMSR RSMXMXR RZMZR RZM_M_R R\\V^X RUVXY RdEdQjW RZC_I RYDRJ R`EjEiUlW RVTS[ R\\TYZ RiRh[ RaJdObW',
	    'Po]GdG RUPbP RUTbT RUWbW Rh[l[ RUMUW RTGYGXL RaGaL RUMbMbW RfFfT RlDl[ R^XcZ RXXR[ RbD\\E\\K RYDTESL',
	    'PoUJbJ RUL[L R]LbL R]OdO RUVbV R\\[d[ Rh[l[ RVFVO R]V]Z R\\J]O RTHbGbJ RTTbSbV RfGfT RlDl[ R_XbY RXCSH RWE^E[G RXOTSUV RSOXP^Q[SZWXYS[',
	    'PoSFZF R\\FcF R[HcH R`LcL RUQbQ RUTbT RUWbW Rh[l[ RUNUW RUNbNbW RfGfT RlDl[ RTDSG R]D[G R^Xc[ RWCVHSL R^H\\L RXYR[ RSHYHYLWL R_EaL',
	    'PoTEeE RVI\\I R^IdI R_RdR RWU[U R_UdU R_XeX Ri[l[ RYFYO R[J[N R_P_[ RaFaO RdJdN RgFgT RlDl[ RVR[Q[WW[ RWKVO R^K^O R[WUX RTETRSZ',
	    'PoVG`G RSIdI RSWZW R]WdW R\\[d[ RR[Z[ Rh[l[ RSTSW RVRVZ RSTZTZW R]T]W R`R`Z R]TdTdW RgFgT RlDl[ RZJ^NcP RWCWFaE_I R]ISL R\\LSN RbKYOSQ R\\RZR R]M]Q',
	    'PoUGdG RVKZK R_KcK RWPcP RXW`W RVZa[c[ Rh[l[ RWMWP RXTWW R\\R\\Y R]H]L R_M_P RXT`T`W RWMcMcP RWScRc[ RUDcDdG RgGgT RlDl[ RZMZPVT RXI[I RYIVI R_IbI_I RUDTRSZ',
	    'Pob[i[ RSJlJlPjZ R^C^L\\QYVS[',
	    'PoY[][ Rc[k[ RdDd[ RWSS[ RlVk[ RXDXMVW RSI_I_U][',
	    'Poe[j[ RZQ[U RSJVNYQ RaV\\[ RYSRY R\\KWU RdCdO_X RSG]GYR R_IlIlTj[ RdIbT',
	    'Po][d[ R[SS[ RiMlT RVMST R]C]NYV R]G\\R RSIfIfRdZ',
	    'PoRG\\G Rd[j[ RWGWT R]SRU R]JlJlRj[ RcDcMbR`W[[',
	    'PocYlY RY[][ RcGc[ RcGlGl[ RXDXMVW RSI_I_U^Z',
	    'Pob[g[ RXHTJ R\\CVJ R^Q\\VYYR[ RUSiSh[ RZGhGeI`L\\MRO RYG\\J`LcMlN',
	    'PoRF_F RX[][ Rf[j[ ReRaZ RaJlJlSj[ RWFWNUX RWM^M^T]Z RfCfLdV',
	    'Po[K_K Rb[h[ R_C_K RgFlJ RYESJ R`O^VZYS[ RURjRh[ ReJ_NZORQ',
	    'PoUL`L Rf[j[ R\\L\\[ RdRaZ RaJlJlUk[ R^DUFUSSY ReCeLcV',
	    'Po]WaW Re[j[ RYCYH RUM]M^W RaT`W RdRaZ RRHaIlIlUk[ RUMUSTY ReDeLcV',
	    'PobMgM RX[\\[ Rf[k[ RWWR[ RdV`[ R`D^I[KUN RVFiFhM RXOWX RfOdX RaRlRk[ RSR^R][',
	    'PoSK`K RRRaR RU[Y[ Rf[j[ RYEY[ RcW_[ ReRaY RaJlJlUk[ RfDfMdV R_DSF',
	    'PoSF^F RRL^L Re[j[ RdR`Z RVLSW R`JlJlSj[ ReDeLcV R[P^VRW',
	    'PoTK]K RTQ]Q Re[j[ RTETX R_JlJlTk[ ReCeLdQaV^[ RTE]E]V RRX]V',
	    'Poc[h[ RbFdKhNlP RTK]O RXCTL RRG^G\\KYNSQ R`EkEiIeM`O R_Q]VZYS[ RTSjSh[',
	    'PoSH_H RRN`N Re[j[ RbW^[ RdQ`Z RYCXNTX R`JlJlTk[ ReCeLcV R]S_WRX',
	    'PoUT[T RX[][ Rf[j[ RULUV RUL[L[T RdSa[ RWCRL RaJlJlUk[ RUH_G_T^Z RfCfMdW',
	    'PoYN^N RTY^Y Rf[k[ RTQT[ RTQ^Q^Y RSE_E_M RWJSO RdRaZ RXEVL R`JlJlUk[ RfCfLdV',
	    'PoTI^I RRO_O Rf[j[ RYCYQ^V RWVRZ RYQUY RdR`Z^[ RUDRL R`JlJlUk[ RYKXU ReCeMcV',
	    'PoSE^E RVX]X Re[j[ RVKVZ RUN]M]W RbV][ RWETR R_JlJlTj[ ReDeMcV',
	    'PoRP]P RRX]X Re[j[ RRHRZ RRH]H]Y RbW^[ RdQ`Z RXCVH R_JlJlUk[ ReCeLcV',
	    'PoTEaE RVJ`J RZ[^[ Rf[j[ RbJlJlUk[ RTETSSZ RZJZPXX RYO_O_U^Z RgDgMeTb[',
	    'PoSQ^Q Rg[k[ RYQYY RaJlJlY RcV_[ RYJ`M ReRaY RSE^EYKRN R_WRY RfCfMdV',
	    'PoRFlF Ra[g[ RSLSO RZCZI RdCdI RSLkLkO R\\UYYS[ R^N\\V RTQiRh[',
	    'PoTPjP Ra[h[ RSISN RSIlIlN RUDXH RjDfH R^C_H RjQh[ R^L]UZXT[',
	    'PoWHgH RRMlM R`[e[ RcKiQlR ReDjI RYDSI RbHdM R_C[LUQRS R]UZYU[ R^O\\W RWSfSe[',
	    'PoTK`K RWX`X Rf[j[ RWPWZ RWP`P`Y ReSa[ RbJlJlUk[ R`DTFTRSZ RfCfMdW',
	    'PoRI]I RSN]N Re[j[ RWWS[ RbU\\[ RTCUG R\\CZG RdP`X R^JlJlSj[ RXIXRWX RdCdLcU R[SRT',
	    'PoSG^G RRM_M RSR_R Rf[j[ RYCYM RYOYY RdR`Z R`JlJlTk[ ReCeMcV R`WRY',
	    'PoRG`G Re[j[ RZCZG R\\J`M RUKRO RaJlJlUk[ R\\NYUT[ RVP^V ReCeMdRaY_[',
	    'PoTO`O RRT`T Re[j[ RYKY[ R[F`J RcW^[ RdQaZ RWCUI RULRR RRJ_JlJlUk[ ReCeLcV',
	    'PoRE`E RTI_I RUO^O RTU`U RRY`Y Rf[j[ RRER[ RZIZU ReRa[ RaJlJlUk[ RfDfMdW',
	    'PoRG_G RRM_M RTY^Y Re[j[ RTQTZ RXCXM RTQ^Q^X RbW^[ RdQ`Z R`JlJlUk[ ReCeMcV',
	    'PoTDaD RhPlP Rc[i[ RdEdL RlClP RZDTJ RUI[L R`P]V[XU[ RWH`H[MSRkSj[',
	    'PoRH`H Rf[j[ RTMXQ R[V_Z R]LVSRT R_QYXRZ RaJlJlUk[ RYDYH RUM RfDfNdTa[',
	    'PoRF\\F RTPWP Ra[g[ RXCXO R^FiFiN RlMlO RbK^O R^IdL R^Q\\VYXS[ RSSiShZ R\\JRK RcCbL',
	    'PoSG`G RTQ_Q Re[j[ RTKTQ RYCYK R[Q[X RTK_K_Q RcW^[ RdQaZ RTZRZ R`WYX RWQUY RaJlJlUk[ ReCeLcV',
	    'PoRE`E RRQ_Q Rf[j[ RYQYY RcV^[ RSKUO RWKYP R]L^P RdQaY RUGSK RYGWK R^G\\K R`JlJlTk[ ReCeLcV R_XRZ',
	    'PoTH`H RRNaN RTY^Y Rg[k[ RTRT[ RZCZN RTR^R^[ RbJlJlY RUDRK RfCfMdW',
	    'PoRF`F RTO^O Re[j[ RU[Y[ RSKSN RYCYK RYRY[ RSK`K`M R]PYQ RaJlJlUk[ RfDeLaZ R^STT',
	    'PoUG`G RTXaX Rf[j[ RUK_K_W RYM[O RXS[U RVCSJ RbIlIlUk[ RUKUQTX RfCfNdX RRQ`Q^[Z[',
	    'PoSG`G RTQ_Q Rf[j[ RTKTQ RYCY[ RTK_K_Q RYQ_V RYPXTRY RaJlJlTk[ RfDeMdU`[',
	    'PoTK^K RTP^P Re[j[ RTGTY RYDYG RTG^G^P RXS^X RbW^[ RdQaY R_R\\VRY R`JlJlUk[ ReDeLcV',
	    'PoUMkM RUQkQ Rd[j[ RUJUQ RUJkJkQ RZFeJ RSUlUj[ RTEkE`H R`J`Q_U[YR[',
	    'PoRO_O RRT_T Rf[j[ RRJR[ RYJY[ RUG\\J RRE_EXH RRJ_J_[\\[ RaJlJlUk[ RfCfMcX',
	    'PoWDUGRK RVF]F[J RTK`K`QTQTK RWQUYR[ RZKZQ R[R\\[l[lX ReDeKcT`W RbIlIkQjWgW',
	    'PoSG_G R[[k[ RYCYK RTKTQ]Q[Z RTK^K^Q RlWkZ RdCdO`W RiWeW RWQUX R`IjIjQiW RdIcS',
	    'PoUI^I Rf[k[ RTMTV RUEUI RUE^E^I RTM_M_V R[W^Z ReSbZ RYVVYR[ RaJlJlUk[ RZPYW RfCfMdW',
	    'PoTI`I RSMbM R[V^V RW[_[ Rf[j[ RWQWZ RVQ^Q^V R_X_Z R]MbR R^DaH RVESH RWORS R\\I_P ReSa[ RYHTR RZDVP RbJlJlUk[ RfCfMdW',
	    'PoRG`G RTQ^Q Re[j[ RV[Y[ RTKTQ RYCYG RYQY[ RTK^K^Q RcW^[ R]S_W RdQaZ RUTRY R`JlJlUk[ ReCeLcV',
	    'PoSG`G RRKaK RSS_S Rf[k[ RYCYK RYPYX R]N`P RVMSQ ReRaZ RbJlJlVk[ RfCfLdV R_WRY',
	    'PoSG`G RaJlJ RRNaN RTY^Y Rf[j[ RTRT[ RYDYG RTR^R_[ RUHVL RdSa[ R]H\\M RlKlUk[ RfDfMdW',
	    'PoTE_E RSJ`J RVPYP Rf[j[ RTRTZ RZIZP ReRaZ R^FYH RbJlJlUk[ RfCfLdV RXRXYRZ R[R[YUY RTR_R_XYY',
	    'PoSGaG Rf[j[ RZDZ[ R^LaP RZP`W RXSRY ReRaZ RVJSQ RUMYOWU R^J\\P RbJlJlUk[ RfDfLdV',
	    'PoRF`F RVJ]J RSR_R RRV`V Re[j[ RSMSR RVCVJ RYJY[ R]C]J RSM_M_R RcW^[ ReQaZ R`JlJlUk[ RfCeLcV',
	    'PoSGXG RSU^U RSY^Y Rf[j[ RSDSM RSPS[ RSP^P_[ R[D[M`L`M RYNWP R_FZH RWLRM RbJlJlUk[ RfCfNdX',
	    'PoREaE RWO\\O RWT\\T RSYaY RSJS[ RWJWY R\\J\\Y RSJaJa[ RYEYJ Rk[g[ RcJlJlUk[ RgDfOdY',
	    'PoRH`H RSO`O RSR`R RSU`U Rf[j[ RSKSR RSK`K`R RaJlJlTk[ R_DSE RZEZY RT[]Y RfDfMdVa[',
	    'PoTH]H RTK]K RSS^S RSV^V RSY^Y Rf[j[ RRDRM RSOS[ RSO^O^[ RRD`D`M ReSa[ RbJlJlTjZ RfCfMdX',
	    'PoVHjH RVKjK R`PlP RTR]R RTU]U Re[k[ RTNTY RVDVK RVDjDjK RdV_[ RlRk[ ReMeRdW RTN]N]XTY R_XWY',
	    'PoSFaF RVJ^J RVM^M RRQaQ R\\U`U RTY`Y Rf[j[ RTQT[ RVCVQ R^C^Q R[Q\\U RXQWV RaJlJlUk[ RfDfMdTa[',
	    'Po`GlG Rd[i[ RRE\\EXI RaE]I R`GeLlO RTHZK RWMSV RRM]M[R RhHdL^O RXMX[T[ R]SkSi[ RdPbV^Z',
	    'PoRF`F RYJ^J RTM`M Re[j[ RTPTV RTDTM RYCYJ R^C^J RTP_P_V R[X^Y ReSa[ RXYR[ RYSXX RbJlJlUk[ RfCfMdW',
	    'PoTI_I RTO`O RTR`R RTV`V Rf[j[ RTKTV RTDTI RTK`K`V R\\W_Z RWWS[ ReRa[ RTD_E_I RbJlJlTk[ RfCfMdW',
	    'PoTM_M Rf[k[ RT[X[ R\\[_[ RSPXPX[ RZP`P`[ R\\J_M RXE\\E`I R_P[T R`S[X RVFRI RWPST RXSSX RXHVM RbJlJlUk[ RfDfOdX',
	    'Po\\IkI RTKZK R[MlM RTRZR Rd[h[ R^D^G RiDgH RTDZDZ[V[ R]SiSh[ RTDTSR[ RdDaJ`N\\R ReIhNlR RcPaW^[',
	    'Pob[h[ RSMSQ RSMlMlQ RXH]J RkEiG RUESH R^E\\G RbE`G ReHkK RdJ_K RWJRL RYCWJ RfCdI R_O]VZYR[ RTSiSh[',
	    'PoSEkE RRRlR Ra[e[ RZCZG RdCdG RdSlV R]OYSRV R^S]W[YV[ RWVfVe[ RVIhIhOVOVI RVLhL',
	    'Pof[j[ RREXEXL R[E`E`L R]O`R RcW_[ RVFSI RVJSM R]F[I R^I[L RZN_P ReRaZ RYNWPRR RZRTT R]TUW R_WYZT[ RbJlJlUk[ RfDfLdV',
	    'PoSDaD RSMaM RTP`P RSSbS RV[Z[ Rf[j[ RSHSM RXDXM RZSZ[ R\\D\\M RSHaHaM R^UaX RVVRY ReSa[ RbJlJlUk[ RfCfMdW',
	    'PoTE]E RRH_H RZK^K RSN^N Rb[g[ RYLYQ RYCYHZK RVITK RaKfM R^R]VZYR[ RTTiTh[ RSQ^Q RdEdMaP RaGjGjQlRmP',
	    'PoSE`E RTH_H RRJ`J RTP_P RTS_S RTV_V Rf[j[ RTMTV RYCYJ RTM_M_V R\\X_[ ReSa[ RVYR[ RbJlJlUk[ RfCfNdW',
	    'PoRF`F RSN_N RSR`R RSU_U Rf[j[ RSJSN RVCVH R\\C\\H RSJ_J_N RaJlJlUk[ RRZ^Y RfDfMdSaZ RYJYY',
	    'Po\\GkG RSHZH R]MjM R\\QkQ RTQYQ Rc[h[ RSDZDZI R]J]M RdHdP R]JjJjM RhNlR RhEkH RaC`G RSHSLZKYQ R^TZYR[ RTTiUh[',
	    'PoSM`M RSQ`Q RSTaT Rf[j[ RSJSP RZJZ[ RSJ`J`P RSFUH RXFZH R]F_I RZSTY RUCSF RZCXF R_C]F RYU`X ReRaZ RbJlJlUk[ RfDfMdW',
	    'PoUF_F RSJ`J RVO`O RVR`R RVV`V RVZaZ Rf[j[ RVNVZ R[O[Z RVCTI RbJlJlUk[ RYGVMRQ RfCfKeRdWb[ R^L[O',
	    'PoTN`N Rf[j[ RZHZN RSJ`I`N R^TaW RWF]FZI RZR_T ReRbZ RYORR RYSSU RXP[TYWRZ RWESITM R_Q[S\\Y R[[X[ RbJlJlTk[ RfDfMdW',
	    'PoRFaF RYI^I RUK`K RTQ`Q RTT`T RTW`W Rf[j[ RTNTW RUDUK RYDYI R^D^I RTN`N`W R\\Y`Z RWXR[ RbJlJlUk[ RfCfNdW',
	    'PoRHaH RVO^O RWX]X R^[a[ Rf[j[ RSQS[ RWJWO RWTWY RWT]T]X RUK^K^O RSQaQaZ R\\HaL RYGSL RSEZEWJ RbJlJlUk[ RfDfMdX R_DXE',
	    'PoSJ`J RTU_U RTY_Y RTQT[ RYQYY RSEZEZO RTQ_Q_[ RUGVJ RVNRO RYJTO R^G\\J RXJ\\L`N RfSb[ RkZgZ RcJlJlTkZ RgDgMeW R_DWE',
	    'PoUH_H RSLbL RTU`U Rg[j[ RZUZ[ R]LbQ R^SaV R^WaY R^EaH R\\PXU RVWSZ RVESH RWLRQ R\\H_O RZNVQZS RfSb[ RYFUO RZCWM RcJlJlUk[ RgDfNeX',
	    'PoSF`F RRJ`J RTP_P RTT_T RSW_W Rf[j[ RTMTT RTM_M_T RYCZF R]G\\J RVGVI ReRbZ RbJlJlUk[ RfCfMdW RYMYZR[ R\\ZVZ R_YYZ',
	    'PoZE`E RZNaN RWPaP R`R^TaY RTHaHaJ R^PWS R\\TVV R]VVY R[C[LVK Rk[g[ RcJlJlUk[ RTHTSSZ RgCgMeX R_JXK R]S^X]ZZ[',
	    'PoRFaF RVM\\M RSO_O RTT^T Rg[k[ RSHSO RVJVL\\J\\M RTQ^Q^W RSH_H_O RbJlJlY RYCZF ReRbZ RZZSZ RfCfMdW RTQTW^XWZ',
	    'Po`JjJ RZM]M R`OjO RRRWRWZ RYR^R^Z R`E`O ReEeO RdSd[iZi[ R`EjEjO RdPgS RWJSN RjTlY RW[U[ RaT_Y R][[[ RXCWK RSG^G]M RUOTSSZ R[PYZ',
	    'PoRFaF RTM_M RTP_P RWW^W RUW\\W R][`[ Rf[j[ RSSS[ RTITP RWCWH RYIYW R\\C\\H RTI_I_P RSS`S`[ R]T^X RbJlJlUk[ RfCfNdX',
	    'PoWD^D RSE\\E RRG`G RTL_L RTO_O RSQ_Q RUT_T RSU]U RfUjU RTITO RYDYT R\\W\\[ RTI_I_O RhWl[ RUXR[ RbWc[ RaU_U RaHkHkOjU ReCeJcR',
	    'PoRGaG RSO_O RSR`R Rf[j[ RSJSO RXWXZ RREYEYU RSJ_J_O RRUYU^V_X RcW_[ R\\L\\N RULVN R[W[Z ReQaZ RTWS[ RaJlJlUk[ RfCfLdV R_CWD',
	    'PoZEaE RYJ_J RZMaM RWRaR RWTaT RY[_[ Rf[k[ RWOWT RYXY[ RZCZG R\\O\\T RWOaOaT RTHaHaJ R`WaZ RZV]W RVWUZ RZIZM RcJlJlUk[ RTHTSRZ RgCgOeX R\\JVK',
	    'PoTDbD RVHaH RVNaN RVQaQ R^ZaZ Rf[j[ RVSV[ RVKVQ RYFYJ R[K[W R^F^J RVKaKaQ RVSaSaZ RbJlJlUk[ RTDTSRZ RfCfNdX R^U^XXX',
	    'PoXEbE RdGlG RZOaO R^QaQ Rb[g[ RVJVR RZJZR RYI^JaJaQ RgMlR RhMcR RWCSH RWHRM R]EZH RdHhP RjHfO RfCcJ RTUiUg[ R^S^U[YS[ R]MZL^LaL]L',
	    'PoSFcF R\\LaL RSNbN RSQbQ RSScS Rf[k[ RWVWZ RTHTLXLXS RTHYHYL R[D[F R^L^S R\\HaHaL R\\H\\L RaU]W RZVaY R[SRX R]YT[ RcJlJlUk[ RgDgOeX',
	    'PoSEaE RTMXM R[M`M RTQ`Q RUS`S RUV`V RUY`Y Rf[j[ RTITM RUOU[ RWCWG R[O[Y R[I[M R]C]G R[I`I`M ReSa[ RTIXIXM RbJlJlUk[ RfDfMdW',
	    'Pob[i[ RVKRO RYCUM RXHlHlQj[',
	    'Pob[i[ RZMaT RVJRO RYCUL RXHlHlRjZ',
	    'PoWNdN RUTfT Rb[i[ RXHRN RZCWIlHlRj[',
	    'PocWgW Rc[j[ RVMdMdW RgTgW RXFRL R[RXVTW R]J[S RYCWHlGlTj[',
	    'Poc[j[ RaQgW R^PaQ RXFRL R_K\\RYUUW RYCWHlGlTj[',
	    'Pob[i[ R\\KWV RXHlHlQj[ RYDWKRN RWVdV RaReX',
	    'Pod[i[ RXHlHlQj[ RYCVLRO R^HYSSX ReHcP`W\\[',
	    'PoWNdN Rb[i[ RXHRN RZCWIlHlRj[ ReTVW',
	    'Po`[f[ RXHYL^ReUlW RXHiHiOhRgZ RYDULRP R`H[UV[',
	    'PoXWdW RYKYV ReSdV RcMXP RZCWHRN RXHlHlQj[d[',
	    'PoVWhW Re[j[ RVMVY R`OeT R_NbR R^OYT RXGRM RYCWJ R`J\\R RXHlHlTj[',
	    'PoVMhM RYWgW Rc[j[ RYMYW R^I`M RXGRM RYCWHlGlSj[',
	    'PoVRaR RcUhU RW[k[ RWRW[ RVKaKaR RWHRM RlWk[ RYCVJ RXGjGjOiU',
	    'Poc[j[ RaV\\[ RWJRO RZRTY RXNgT RZCUL R^IYT RdN_X ReHbU RXIlHlRj[',
	    'Poc[i[ RaTdW RXHRN R\\MVS R[QbT RZCWJ R]J[NeMbS]XWZ RXHlHlQj[',
	    'PoVWfW Rd[j[ RVLVZ RfMfW R^OcT R`NXU RXFRM RYM^O RaJ^P RYCWHlHlTj[',
	    'PoWKfK RTThT Rc[j[ R^K^[ RXFRL RXMYQ RdMbR RYCWHlGlTj[',
	    'PoTQhQ Rc[i[ R^J^[ RXKYO RYVTX R^PWX RdKcO RWIRN R^SeU RYCVJ RXHlHlQj[',
	    'PoXLeL RUQgQ RWWeW Rc[j[ RWSWY R^L^W ReSeX RXGRM RYIWO RYCWIlHlSj[',
	    'PoYObO RWXcX Rd[j[ RWSWY RWScScX RYFRL R^JaMgP R\\JYNTP RYCWHlGlSj[',
	    'PoVKhK RWRfR RWUfU RcZf[j[ RWOWZ R^I^Z RWOfOfY RXFRL RbIfJ RYCWHlGlUk[',
	    'PoUJhJ RWRfR RWUfU R^YhY Rf[j[ RXNXV R_L_X RWNgNgUhY RXFRL RbJeMjO R]HXNSP RYCWHlGlUk[',
	    'PoSF_F RUM]M RSQ_Q RaRgR RfUjU Rb[k[ RX[\\[ RbKb[ RaKgKgR R[I^L RlWk[ RXHTL RXH\\J RcC`K RYCXJ RWQUU]U\\[ RbGkGkQjU',
	    'PoWJgJ RXOeO RWUfU RWXfX Re[j[ RWRWZ RXLXO R^R^X RXLeLeP RWRfRfX RWHRM RYCVI RWGlGlTj[',
	    'PoWM]M RaNgN RWP]P RZZ]Z RbZf[k[ RWJWT RWJ]J]Z R`JgJgN RXFRL RZHZK R^QYVTY RYCWHlGlUk[ RaN`SgSfZ R]STT',
	    'PoWJiJ RZRdR RXYfY Rf[j[ RXTXZ R[H[M RcHcM RXTfTfX RXFRL R^MiR RXCVI R_MTR RWGlFlUk[',
	    'PoUN]N RUQ]Q R_QhQ RUKUQ RUK]K]Q RVRVX]W\\Y RaJfJfOiNiO RXFRL RaSeXhY RgScW_Y R]SUU RaJ`O Rj[f[ RYCWHlGlUj[',
	    'PoUN]N R`OhO RUQ]Q R_XiX RX[][ Re[j[ RUKUS RUK]K][ R_R_Y R`J`O R`JhJhO R_RiRiX RXERK RYHXK RRT]SZVSY RWGlGlVk[',
	    'PoS[j[ RSDSZ RlUj[ RhIRP',
	    'Po`[k[ RXIX[ R`D`Z RXIRO RZDWJ RgK_O RlVk[',
	    'PoSK[K Rb[k[ R[D[[ RbDbZ RhKbN R[URW RlUk[',
	    'PoRWXW R[ZkZ RSDSW RYSXW R[N[[ R[NkNk[ RdThX RZH^K RaHeL RhHlL RaLaO RkDgG R\\C[H RdDaG R_RdT RgPbV^X RWKRM',
	    'PoTH_H RTL_L RRO`O RZT`T RcVkV R\\[l[ RTDTL RTD_D_L RcDcV RUUYY RlSkV RkIcM RUSS[ RZOYZa[',
	    'PoREkE RRYlY RRER[',
	    'PoREkE RRZlZ RRER[ R\\JVP RbOdRgVkW R]G[KdKbO]TVW',
	    'PoREkE RYVhV RRZlZ RRER[ RYHYU R`F`S RhShV RfJfQcQ RfJTN',
	    'PoREkE RdUhU RRYlY RRER[ RWKWU R`G`X RWKhKhT',
	    'PoRElE RWJiJ RUTjT RRYlY RRER[ R_J_T',
	    'PoREkE RUJjJ RdVjV RRZlZ RRER[ R_G_J RZMdMeV RjSjV RZSVW RZMZT',
	    'PoREkE RXNjN RRYlY RRER[ RbNbW RXIXOWU RfHXJ',
	    'PoREkE RWIiI RXOhO RVUjU RRYlY RRER[ R`I`U',
	    'PoREkE RR[l[ RRER[ R_RZW RYJTO R[OVT RZGWM R]JZQ RbJ]T RdW`W RXKgKeW',
	    'PoREkE RWMiM RWRiR RRYlY RRER[ RWIWS R`I`W RWIiIiS',
	    'PoREkE RVIhI RXOgO RUUiU RRYlY RRER[ R_I_U RcQfT',
	    'PoREkE RUPkP RRYlY RRER[ RdMdW RVLgKiM R[SWW R]HYL RcIgK R[M[T',
	    'PoRElE RUJ^J RUP^P RhXkX RR[l[ RRER[ R[L[Y R`LhLhW RkTkX RYGVO R`W_W R_SUU RcGcObU',
	    'PoREkE RUJjJ RRZlZ RRER[ RYN\\Q ReNhQ RaQdUiW RYLWQ ReLcQ R^Q[UUW R_H`N^R',
	    'PoREkE RWM\\M R^MkM RUP\\P R^VjV RRZlZ RRER[ RWHWN R\\G\\W RdGdV RXPVW',
	    'PoREkE RWKhK RWOhO RXRgR RUVjV RRZlZ RRER[ RWHWL R_G_M R_O_V RhHhL',
	    'PoREkE RUJ]J RbJjJ RbNhN RbSjS RRZlZ RRER[ RbGbW R]G\\SXW RVN]M\\SUS',
	    'PoREkE RcNfN RXTgT RXWgW RRZlZ RRER[ RXPXW RXPgPgW R[FVK R^HYN RcJ^O RYIhIgN',
	    'PoREkE R\\KdK R[W^W RRZlZ RRER[ RWNWW RWN^N^W RaRcW RfShW RYHaHkL RYP\\Q RYS\\T RhNfR RcNaR R_HUM',
	    'PoREkE RUI`I RVO_O RVR_R RUU`U RhWkW RRZlZ RRER[ RVLVR RZGZX RVL_L_R RaKhKhW RkTkW RcFcObU',
	    'PoRDkD RWKhK RTNkN RR[l[ RRDR[ RWGWK RXQXV R_F_N RXQhQhV RWGhGhK RaWjY R`S^WUY',
	    'PoREkE R]KjK R]NiN R]RiR R]VjV RRZlZ RRER[ R^J^W RdIdV R^JYN RVGYI RXRVW R_G\\M RTLXM',
	    'PoREkE RVJ]J RaJhJ RWPgP RWSgS RUUjU RRZlZ RRER[ RVGVJ RWMWR RVG]G]J R_M_X RaGaJ RWMgMgR RaGhGhJ',
	    'PoREkE RXJgJ RULjL RXPgP RXRgR RXUgU RRZlZ RRER[ RXNXU RXHXJ R_F_L RXHfHfJ RXNgNgU RbViX R\\VVX',
	    'PoREkE RUIkI R[PeP RWW^W RbWiW RRZlZ RRER[ RWRWW R[K[P RWR^R^W RbRbW RbRiRiW R_GZLeKkN ReKdP R[KUN',
	    'PoREkE R[KcK RXR^R RaRgR RRZlZ RRER[ RXNXR RXN^N^R RaNaR RaNgNgR RcUgV RZT]W R[SVX RdS`X RaHjL R^F\\IUL',
	    'PoREkE RWI_I RbIkI RYNgN RYQgQ RYSgS RUVjV RR[l[ RRER[ RYLYS RbFaJ RcScZ RYLgLgS RYFUJ R[XWY R\\S[X',
	    'PoREkE RVGiG RWIhI RVNhN RRZlZ RRER[ RVKVN R_F_I RXPgPgV RVKeLhLhN RdRgR RbWiX R\\WUX R]LXM RXPXV[V]V[V RXTgUdV R_RbR RXR\\R_R R`R]R RcR`R R^VaVdVaV^V',
	    'PoREkE RUHiH RXMgM RXOgO RXQiQ RWU]U RaUgU RWWgW RRZlZ RRER[ RWSWW RYJYQ R[F[I R`J`Q RcFcI RaSgSgW RYIUN R]SWS',
	    'PoREkE RaJiJ RVJ]J RaLiL RVL]L RaNiN RVN]N RWR`RhS RWVhV RWYiY RR[l[ RRER[ RVGVN RXQXZ RVG]G]N R`O`Y RaGaN RaGiGiN RXQSS',
	    'PoRGlG RW[k[ RVGVZ',
	    'PoRElE RRZlZ RRER[ R[EZOUS RdEdRjRkN',
	    'PoREkE RRYlY RRER[ R`OcQgU RWJ\\L`N ReIZTVV',
	    'PoREkE RYJiJ RUOkO RRZlZ RRER[ R[HVL R`J_PdUjW R_Q\\UVW',
	    'PoRElE R\\MeM RZWgW RV[l[ RTETY RZQZW RZQgQgW RaGeKkN R`G[LVN',
	    'PoREkE RXKgK RXNgN RUQjQ RRZlZ RRER[ RXHXN RXHgHgN R^N[QYT RZShW ReQaUVX',
	    'PoRFkF RYIcI RYNiN R[TiT RgXiX RV[l[ RTFTY RYHZR[X R_Q_X RdQdX RYKiKiN R[QiQiX RYMXV RfH`H',
	    'PoREkE RVIiI RUNjN RRZlZ RRER[ R[G[K RdGdK R^MWT R[QgQgW[W[Q',
	    'PoREkE RZMeM RWV^V RaViV RRZlZ RRER[ RWPWV RZHZM RWP^P^V RaPaV RZHeHeM RaPiPiV',
	    'PoRNlN R_C_[',
	    'PoROdO R[G[[ RlWk[ RREgEgQiZ',
	    'PoRNlN R_F_[ RhDZFUG',
	    'PoRNlN RZDZ[ RdDd[',
	    'PoRMlM R_E_Z RhDh[ RWDWSS[ RWMUW',
	    'PoeOlO RRSlS R_O_[ RTGeGeN RlKlO R]E[JXMRO',
	    'PoRNlN ReDe[ RXVS[ R_ESG RYFYPXV',
	    'PoWHjH RRPlP R_H_[ RXCVIRL',
	    'PoTGkG RRQlQ R_C_L ReLe[ RXVS[ RYLYQXV',
	    'PoTLjL RRSlS R_C_[ RVDXJ RhDfI',
	    'PoSGkG RRPlP RWYgY RWLW[ R_C_L RgLg[',
	    'PoRMlM RVDV[ R\\D\\[ RbDb[ RhDh[',
	    'PoRPkP R_[l[ RRPR[ RRE_E_[ RkEkP',
	    'PoaOkO RRTlT RXFXP R_Q_[ RaDaO RkLkO R[CSK RjF\\J',
	    'PoRJYJ Ra[f[ RVCV[ R^UY[ R\\MZT RaO]X RjMkT RbDbL`S R[IhIhUf[',
	    'Po_FlF RRZ_Z R_F_Z RSESPkPk[',
	    'PoUKiK RUPiP RRVlV RUFUP R_F_P RbPb[ RUFiFiP R^D\\F RYRXV',
	    'PoSGkG RRTlT R_R_[ RdLhNkQ R_C_G RYIXMTQ RXM]O ReIdM`Q',
	    'Po_FkF RUNiN RURiR RRVlV RUJUR R_C_I R_R_[ RUJiJiR',
	    'PoRKZK RfMiM RZRbR RVCV[ RdRlRlZ Ra[^[ Rk[h[ R\\GjGiM R^O\\Y RaRa[ RgOfX RcCbI`L\\M',
	    'PoUMiM RURiR RRVlV RUHUR R_H_[ RUHiHiR RXD[G RfDbH',
	    'PoTGjG RRSlS R_C_K RXM]N RUPZQ RSKkKiO RaWjZ R_T[YS[ RaL_U',
	    'PoRGlG RXQfQ RWUgU Re[j[ RTKT[ R_C_K R_Q_[ RTKjKj[ RZM[O RcMbQ',
	    'PoUMiM RURiR RRVlV RUHUR R_H_[ RUHiHiR RUDXG RhDdH R]C_G',
	    'PoRF`F RUJ]J RaMlM RUM]M RRQaQ R[U_U RTY`Y RTQT[ RUCUQ R]C]Q RfCf[ RXQWV R[Q[U RVVTV',
	    'PoZGlG RRKZK R\\MjM R\\QjQ RZTlT Rc[g[ RVCV[ R\\J\\Q RcCcQ RgQg[ R\\JjJjQ R^VaY RfDjE',
	    'Po\\DbD R[GcG R\\IbI R\\LbL RRLYL RdLlL R\\QbQ RRVlV RWLWS R\\N\\R R_S_[ R\\NbNbR RhLhS RWJZL RiJlL RdFhHeL RXFUI RUCSGVHSL RgCfG RXNYQ RkNkR ReNdR RSNRR',
	    'PoYCY[ RZLlQ R[LXL',
	    'PoRN[N RcNlN R[D[[ RcDc[',
	    'PoRIlI R_C_[ R_NjS',
	    'PoRUYU RRFRW RRFYFYT RaDa[ RhMkQ',
	    'Po]IlI RRYiY RRPR[ R]C]P RRPiPi[',
	    'Po_HiH RRNlN R_C_[ R_RiT',
	    'Po_FlF RVRiR R_C_K RVKiKiS RVKVRUX',
	    'Po^FlF RRUbU RRZjZ RRJR[ R^C^J RRObObU RRJjJj[',
	    'Po^FlF RRZjZ RRJR[ R^C^J RRJjJj[ RbSeW RWNaR RdL^SdT R^SVW',
	    'Po^FlF RRZjZ RRJR[ R^C^J RRJjJj[ R]LYP`S RZOUR R[OeO`TWX',
	    'PoSG_G RRMaM RSS`S RYCYM RdCd[ RdMlP RYOYYRZ R^XVY RaXYY',
	    'PoRE_E RRR_R RRY_Y RRER[ RYEYK RYRYY RRK_K_R RcDc[ RcLkP',
	    'Po^FlF R^LhL RYYfY Rf[l[ RRQR[ RVIVQ RYTYY R^C^I RYTfTfX RVIhIhQ RRQlQl[ R^L]Q',
	    'PoaUjU RTET[ RTEiEiU',
	    'PoaUjU RTET[ RTEiEiU R]JbO',
	    'PocSiS RR[l[ R^F^[ RTFiFiS',
	    'PofWlW RUFUW RaFa[ RaFlFlW R^DTG R^URV',
	    'PoRF^F RfUlU RXFXT RaEa[ RaElElU R^SRU',
	    'PoUE_E RUJlJ RaVgV R[[j[ R[N[Z RkWk[ RUEUQTY RZNhOgV RhD`E',
	    'PogVlV RTFTU RbFb[ RbFlFlV R\\UYYS[ R^DTG R]I]SRU R]M\\V',
	    'PoRM\\M RRV]V RgWmW RRFRX RaEa[ RaEmEmV R]DRF',
	    'PoUJlJ RbUfU R[[k[ R\\N\\Z RZESL RlWkZ R[NgNfU RVIVQTZ RZFdFaJ',
	    'PoZN_N RfVlV RTY_Y RTQT[ RTQ_Q_[ RcEc[ RcElElV RWJSN RXEWL RSE`E_N',
	    'PoTJ^J RTP^P RgVlV RTETY RTE^E^P RbEb[ RbElElV RZS^V_Z R]WRY',
	    'PoSH_H RRO`O RgVlV RSX_X RcEc[ RcElElU R]T`Z RYCYNTX',
	    'PogWlW RTFTU RcFc[ RcFlFlV RXKYO RfKhP R\\UYYS[ R^DTF R]H]SRU R]M\\U',
	    'PohVlV RdEd[ RdElElV R`RZX RZCTI RWG_GXM RYPaP^U RWJ[LSP R\\MXRRT RYT\\WS[',
	    'PoWHgH RRMlM R_VdV RY[g[ RZQZ[ ReDjI RhXg[ RYDSI RWPRR R[KTR RbHcKhQlR R^CZM RYQeQdV',
	    'PoVG`G RSL`L RZQ`Q RgWlW RUPUY R[G[X RcEc[ RcElElV RWCTJ RaWRY',
	    'PogVlV RTHTX RXFXW R[H[V RcEc[ RcElElV RYCWH RTH_H`URX',
	    'PoTQkQ RaXgX RV[j[ RWTW[ RVTgTgX RjXj[ RbGfLlN RSH[HXLRN RiHdJ RVEgE_H_N[N',
	    'PogUlU RUY^Y RUQU[ RUQ^Q^Z RcDc[ RcDlDlU RZJ`O R[D_H RYDTI RZJSQ',
	    'Po]KjK RZ[k[ RRF[FZZ RbMbXjWjXl[ RaFfFdJ RbMhMhSeS RVFVQTY RaC^J^R]X',
	    'PoRJ^J RRO^O RgVlV RSZ^Z RRFRO RSQSZ RRF^F^O RbEb[ RbElElU R^W^Z RXCUF R]SRU',
	    'PoSJXJ R\\JaJ RTM`M RRQbQ RgVlV RY[^[ RSESJ RSEXEXJ R\\E\\J R\\EaEaJ RdEd[ RdElElV RWQUU_U^[',
	    'Po\\KcK R\\PcP R\\TcT RiVlV R\\XdX RTETT R\\G\\[ R_C^GcGcP RfEf[ RfElElU RXVS[ RZDTE RYHYSRT RYNXW',
	    'PoRFbF RgVlV RW[Z[ RZCZK RZQZZ RdEd[ RdElElU RURWT R_R\\U RYF`I RZMbQ RZUbX RZLWORQ RZUSY RZFVJRL',
	    'PoUI^I RSM`M RVRZR RdYiY RZIZR R]T][ RhDhS RTTjTjY R]O_Q RUNTR RbJfK RYDaH RcFfG RZDWFRH RjNaO',
	    'PoUFlF RUFURSZ',
	    'PoVKlK RZERM RYGcF_K RVKVRUX',
	    'PoUElE RaSgS R[[k[ R[J[Z R[JhJhR RlVkZ RUEURSZ',
	    'PoUElE RXKkK R][b[ RbKb[ RUEURSZ',
	    'PoUElE Rb[h[ R]VY[ RXMjMjRh[ RUEURS[ R`G`M^U',
	    'PoUElE R`[k[ R`J`Z RkWk[ RhHXL RUEURSZ RfP_QYR RcQ\\Q RjPdP',
	    'PoUElE RYJkJ RWQlQ RaJa[ RUEUQSY',
	    'PoUElE RXJlJ Rb[g[ R[VW[ RUEURSY R^J\\V R^PiPgZ',
	    'PoUElE RYIkI RZQlQ Rb[f[ RfIf[ ReQaUXY R[KZQ RUEUQSZ',
	    'PoTElE RXNjN RVZlZ RaHaZ RfRiV RTETRS[',
	    'PoUElE RXMkM RdTk[ RfHhK RaMeU RaO^VZYV[ RUEURSY RaH`S',
	    'PoUElE RXKjK RZQjQ RWVlV RcMc[ R^HZQ RUEURSZ',
	    'PoUElE RZOlO RWZeZ RZJZW RkUkY RfXjY RbIdT RUEURSZ RhH[J RbVWX',
	    'PoUElE R[KlK RbPkP RbUkU RbJb[ R]GYO RUEURSZ',
	    'PoUElE RXMkM Rc[k[ RcMcZ RdIgJ R[VV[ RlWl[ R]QZX RhPcV]X RUETRS[ R^H^L\\U',
	    'PoUElE RYX_X RdZkZ RYIYY RYI_I_X RdHdZ RkWkZ RkKhOcQ RUEURS[',
	    'PoUElE RZKjK RWQlQ RYYjY RYTYY RaJaY RjTj[ R\\GZL RUEURSZ',
	    'PoUElE RYJjJ RWOkO RYTjT RVZlZ RaFaO RaPaZ RUEURSZ',
	    'PoUEkE RWIkI R]PhP RXPdP RYTjT RVZlZ RaOaZ RfLkQ R]MZP R_I]N RUEURSZ',
	    'PoUElE Rf[k[ RYIYU RYIbIbU RfJfV RkGk[ R_Wb[ R\\VX[ RUEUQSZ R]K]R\\W',
	    'PoUElE RWLaL R][k[ R]O]Z R_H`J RYWU[ RlWk[ RiJaM RiObR RjTaX RUETRS[ R[G[NYW',
	    'PoUElE RYJjJ RWQlQ RYUjU RbQb[ R`GcJ R\\L^O RfLeQ RUEURSZ',
	    'PoUElE RYMiM RYVjV RW[l[ RaIaZ RUEURSZ RYIjIjRYRYI',
	    'PoUElE RWIkI RXPjP RXTjT RVWlW RXLXT RaGa[ RXLjLjT RUEURSY',
	    'PoUElE RYKhK RYNhN RYRhR RWVlV R\\[a[ RYHYN RaTa[ RYHhHhN RhRaT RUEUQSZ',
	    'PoUElE RbOkO RXV^V RXIXY RXI^I^V RhOh[ RUEURSZ RbJbR`Z RjIcJ',
	    'PoUElE RXLkL RWQlQ RXVkV RXZiZ R[L[V R[IaHbZ RgLgV RUEURS[ RhG_H',
	    'PoUElE RXIkI RWNlN R[ViV R[ZiZ R[Q[[ R]F]N RfFfN R[QiQi[ RUEUQSZ',
	    'PoTElE RWK^K RdKkK RXP^P RdPjP RWU^U RdUlU R^H^[ RdHd[ RTETSR[',
	    'PoUElE RYNiN RYRiR R\\[a[ RYIYR RaRa[ RYIiIiR RgUlZ R[UVZ RaE_I RUEUQSZ',
	    'PoUElE RYM`M RYQ`Q RYU`U Rf[j[ RYIYU RYI`I`U ReJeV RkHk[ R_XaZ RZXW[ RUEURSZ',
	    'PoUElE RZLiL RZPiP RZIZP RZIiIiP RhTeU RjRgT RaPcSgXlZ RaE`I RWS_T[XVZ RaPa[][ RUEURSZ',
	    'PoUElE RVL`L RbOkO RbTkT RbZkZ R[G[[ RbIb[ RbIkIk[ R[N`S R[LWU RUEURSZ',
	    'PoTDlD RWJkJ RYSiS RYViV RZNZW RaOa[ RWOjOiW RfGiH RcKkO RaF`J]LWO RTDTSSZ',
	    'PoUEkE RVM`M RaMkM R\\H\\[ RgHg[ R[N_S RcS_W RfLjV R[MWV RfLbU R_GVI RjGaI RUEURSZ',
	    'PoUElE RWLaL RXT`T RXNXU R`N`U RhWl[ R[WW[ RdW_[ RXGZJ RgSjY ReRcY R_G]J RcLkLjQ RdGbQ RfOdW RfMgV RUETRS[ R\\L\\T[X',
	    'PoUElE RWHkH RZMhM RZPhP RZShS RZKZS RaH`K RZKhKhS R^TVX R[VaYj[ R[VfVaYV[ RUEURSZ',
	    'PoUElE RXHkH RZNhN RZQhQ RZThT RWWlW RZKZW RZKhKhW R`F_K RdYl[ R^YV[ RUEURSZ',
	    'PoUElE RWIaI RbLlL RXR`R Re[i[ RXLXR RXL`L`R RiGi[ RYTYX RcPeT R^T^X RUETRS[ R^YVZ RaYYZ',
	    'PoUElE RbIkI RXM_M RXP_P RaPlP RXT_T RXW_W Rg[l[ RXIX[ R[G[I RXI_I_P RbIbP RgNg[ RlWl[ ReU`Z RgPcX RhIeU RUEUQSZ',
	    'PoUElE RWHkH R]KfK RYQjQ RXTjT RYWjW RVZlZ R]F]K RaKaZ RfFfK RYNjNiQ RYNYQ RUEURSZ',
	    'PoUElE RWI`I RVMaM RVQaQ R\\[_[ R\\G\\M RgVk[ RgVb[ ReQhY RiPeX RXZV[ RdLgW RdLkLgW ReGbP R\\N[QYY RUEURS[ RZU_U_[',
	    'PoUElE RZJiJ RZMiM R^TlT RZZhZ RZGZM RZVZ[ RZVhVh[ RZGiGiM RfOcS R[NXQ RePjR RZP`P]SWV R\\SdT RUEURSZ',
	    'PoUElE RXJ_J RXM_M RaMlM RWS_S RWV_V R\\[_[ RWPW[ RXHXM RXH_H_M RWP`P`Z RiHkJ ReSb[ RfGfNiX RUETRS[ RfKdW',
	    'PoUElE RVJaJ RYM_M RdOlO RYQ_Q RVUaU RYGYU R_G_U RiOi[ R^W`Y RYXV[ RUETRS[ RkHcJcSbY',
	    'PoUElE RWJ_J RaJkJ RZ[e[ R[T[[ R[G[R RfGfR RZJ_N R]TbV RgUlZ RhLlP RfHhM RdL`P ReXe[ R[HXO RUEURSZ',
	    'PoUElE RWMW[ RZPZW R\\G\\M RgVl[ RgVb[ R_H`L ReQiY RiPeX RdMgW RdLkLgW ReGbQ RZP^P^VZV RXHWM`M`[][ RUETRS[',
	    'PoUElE R]LfL RYT_T RdTjT RYOYT RYO_O_T RdOdT RdOjOjT R\\W_Z R\\VWZ RfVa[ ReWjY RaGjL R`HXL RUEURSZ',
	    'PoUElE RVHlH RZNhN RZQhQ R[XgX Rf[j[ RXTX[ RZLZQ R]F]J RaLaX ReFeJ RZLhLiQ RXTjTj[ ReUhY RUEUQSZ',
	    'PoTHkH RWJ`J RXQ_Q RXU_U RXY_Y RXNXY RVN`N_[ RVDXF RiDfF R^C_F R\\J\\N RdJbR RTHTSSZ RdOgUk[ RcMkMgVc[',
	    'PoTDlD RaJlJ R\\Q_Q RYUjU RYXjX RYSYY RaSa[ RYSjSjY RhMlQ RhFjH RfHiO RcPaP RWLWPZOWO RWM_L_P\\O R[HWGXJ[J RfFeN RXF]G_G_J[J RTDTRSZ',
	    'PoVEjE RYKhK RTP_P RaPlP Re[g[ R[PXR RaE_G RgWg[ RYGhGhK RVW]W^Y RcRcVjWlZ RWXU[ RdXbZ ReLiN R[LWN ReIhI RcTgT R[T^T RYGYJ\\IYI R`K`N]N RVRWVZTVT RVR^R^UZT RfPdRkRkUgT R^I\\I RaI_I RZWZ[X[ R]I`IcI RVFUM RTPTUSZ RaPaU`Z RdIbI',
	    'Po\\YkY RXYfY RSYaY RcPhT RiVk[ R]DVY',
	    'PoRHlH R_YkY R\\YgY RXZdZ RiWk[ ReSjX RVVRY RZLTX R`N_S[Y R\\EWT',
	    'PohOjSl[ RXETY ReEaY R[O^YRZ RkX`Z',
	    'PoeOlO RWYiY RUZgZ RTGeGeO RlLlO RdSjY R\\PXX R[JWNRP R]CZK',
	    'PoRHlH R_YhY R\\YdY RXYaY RUZ_Z RbRfVi[ RaHhOlP R^NXY R^C\\IVORQ',
	    'PoTHjH RROlO RWYiX R_C_O RfUkZ R\\OWY',
	    'Po_KiK R]LgL RZLdL RTL_L RZ[_[ R_M_[ RePlV RdFlN R\\DTL RWRRW RXOVT',
	    'PoZLhL RWLeL RTMcM RcGjN R[T^[ RhTl[ R\\CXL RWOTZ RdOaZ RXYRZ R]YVY RjXbY',
	    'PoSKdK RRQeQ R]YdY RZYaY RTY\\Y R`UfZ R\\G[QXUTY RlWk[ RSEgEgQiZ',
	    'PoWIgI RWNgN RRSlS RYZiZ RUZfZ RWEWS RWEgEgS RdUj[ R\\SUZ',
	    'PoRFlF RUNiN RURiR R[ZjZ RTZdZ RUIUR R_C_R RUIiIiR ReTk[ R[RYX',
	    'PoTIiI RSMlM RWVgV RT[k[ RcMhR RfGjJ R[C[FXI RXQfQlS RcEhG R]I\\MWRRS',
	    'PoTHiH RSLkL RgFjJ R[EXH RcLgPlR R]JXPRR R`OYR RdR_UYV RhVaYX[',
	    'PoRM\\M R_JbMjLkN R\\CXG RbMlR RTGeFhG RWIUM ReIcM R^JZNRR R`OWS RcR^UXW RgUaY]ZV[',
	    'PoTHhH RRN\\N RXRfR RWVgV RT[k[ RZK\\N R_KcOjLkN R[EXH RcOlS RcEjH RWIUN ReIcM R^KYPRS',
	    'PoTHhH RaMkM RRM\\M RZ[][ R]O][ ReRiW RiKkM R\\DXG RaScX R_JcNlR RcEjH RWIUM RXSVX ReIcM R^JZORR',
	    'PoSFZF RRLZL R]OkO R\\K\\M R\\KkKkM R_UZZ RYQ[W R^G_I RcFdI R`SdXk[ RiFhJ RaO]X R`SiSeX^[ RVLSV RaL^V RZURV RjC_E',
	    'PoSFYF R`JlJ RRMYM RbMjM RcXfX Rb[l[ RfCfX RbFjFjM R\\M^M^WaZ RaPdR RfQkV R^WZ[ RkOgS R]D]I RfS`U RUMRX RXQYWRW R^M[M',
	    'PoXI[PaWk[ RTGiGeO]VR[',
	    'Po\\IaM RWH[PaWj[ RTFhFdO^VR[',
	    'PoSEdEbMjMgRcW]ZX[ RYEYLXPUWRZ RZH^RdXl[',
	    'PoSHkH RZNgNeSaWX[ RZM\\RaWfYl[ R[DZKXQVVRZ',
	    'PoTKWNZQ\\X RSF]F[MXUR[ R`FjFfS_Z RaLbPdUk[',
	    'PoVEkE RVLiLfQaWW[ RZL\\RaWfZl[ RVEVQTY',
	    'PoTFTU R[C[[ R[SRU R^FkFgQ^[ R`GaNeUj[',
	    'PoRLlL R_C_P RUFgFgL RWP\\VbYj[ RTPgPcU]XR[',
	    'PoTF[F RRJlJ RZOhO RXSSX RZN[QaWj[ R[KWU RgQbWX[ R\\FYR RhD\\E',
	    'PoUJkJ ReDhG RZQRY R\\N]RbWj[ RXDUJ R]IXT R[OgOeSaWY[ R^D[Q',
	    'PoVT[Z RUQWW RbQcUgYl[ RSP\\PZUVYR[ R`PjPhUdX_[ RYG_KhM RUEgEdH^KUM R^KjM R_KSM',
	    'PoRFkF RUNhN RUJUN R_C_R RUJhJhN RTRgRdU^XU[ RWR[VaYl[ R`XR[',
	    'PoXG^G RRM`M RU[Y[ RYCYZ R]Q^V RUQRW RaFkFiNfU_[ RbFdPgWl[',
	    'Po`EjE RYI\\M RYU]Y RgJjM RTG[J RaQcTgYl[ RTS[V RaGiK RSE\\E[IWLSN RiFeK_N RSP]PZUVYR[ R`PjPhTdX^[',
	    'PoUK]K RUP]P RUEUW R]URW RaHkHgRaZ RaJcPfUk[ RSE^E R]E]Z',
	    'PoRKRO RRKlKlO RVGWJ RhHdK R^F_J RWP\\VbYk[ RUPgPcU]XS[ RiC_ETF',
	    'PoRFlF R[F[O RbFbO R^C_F RfIkN RWJTN RWQ[UaXeZl[ RTQgQcU]YR[',
	    'PoUK^K RSP`P RYKY[ RZC_H RZCRK R]S_W RUSSY RaFkFiOfUa[ RbFcLeSl[ RY[V[',
	    'PoRL\\L R_LiL RRP\\P RRV\\V RRER[ RRE\\E\\L R_EiEiL R`PaTeXl[ R^PjPhTdX][',
	    'Po_EkE RSL\\L RdTjZ RZF\\J RaMeV RTERJ RXCWST[ R_LjLgUbY_Z R\\QRS RXLVW R_E_Q^X',
	    'PoSFSI RSFkFkI R^C`F RgIdL RYIUM R_KjQ RWRZU_XhZ R`H^L[OTQ RUSgSdV_XV[ R^Xl[ R`XR[',
	    'PoSH]H ReKjK RRN^N R`DkDkK RdTk[ RbOfW R`NkNhUcY`[ RXCWNTW R`D`Q^Y R\\S]VRW',
	    'PoaEkE RTJ_J RWN_N RVR`R RX[[[ R[R[[ RTD_D_J RgVl[ RfT`[ R^T`X RdOiY RjLdW RWTUY RkFgU RcGfU RTDTSSZ',
	    'PoUJ[J RbJiJ RUO\\O RbOiO RUEUO R_C_R RbEiEiO RXRXT R[DTE RYU_XcYl[ RRShReU`X\\YR[',
	    'PoVDgD RTJ]J R`JkJ RWVgV RWXgX RR[l[ RSPSS RWSW[ R_N_P RWSgSg[ RSPkPkS RaLkN RUL]N R\\KXNRO RjKeM`O RXFgH RfE_GUI',
	    'PoYE_E RVJ]J RUU^U RUW^W RUZ^Z RSHSJ RVQV[ RYCYG RTS^R^Z RSH`H`K R\\L_M RgVl[ R[N`S RYN]O RfU`[ RVLSO RYMTR RdOiY RjLeW RaFkFgU RcGfU',
	    'PoRGlG RTKkK RUNiN RRPlP R_SjS RUS]S RUV]V RTY]Y R\\C\\G R]P][ R_K_P RZH[K RVDWG RdGcK RhDfF RaTdXk[ RiTfX_[ RbCbG RUPVYRY',
	    'PoRYlY RRFR[ RRFlFl[',
	    'PoRHlH RVZiZ RVPV[ R_C_P RVPiPi[',
	    'PoXVcV Rb[i[ RXMXX RXMcMcU RXHlHlRj[ RYDVKRN',
	    'PoVMkM Rd[j[ RVEVM RVEkEkM R^TZXT[ R`M]V RRQlQj[',
	    'PoVLjL Rc[i[ RVEVL RVEjEjL R_P]VZXWZR[ RRPlPj[',
	    'PoRUYU Rc[i[ RRFRW RRFYFYT R_UZ[ RbP^X R\\FlFlQjZ RcFaT',
	    'PoRS[S RfVlV RRERV RRE[E[S R`E`[ R`ElElV',
	    'PoURjR RUEUS RUEjEjS RhXl[ RcUgW RZUWYR[',
	    'PoRSZS RRFRV RRFZFZS R_F_U RhDh[ RlT[V',
	    'PocOjO RWZkZ RWQW[ RWQkQk[ R_E]JZMSP RTElEjN',
	    'Po^EfE RRSYS RRFRU RRFYFYS R[VW[ RhSk[ R^PZY R_J\\U RfGhW',
	    'Po[FlF RRUYU R_[e[ RRFRW RRFYFYU ReFe[',
	    'PoRElE RUTaT Rb[h[ RUKUV RUKaKaT RhEh[',
	    'PoSLjL RVZhZ RVQV[ RVQhQh[ ReGlN R[DVL',
	    'PoRTYT Rb[k[ RRFRW RRFYFYT RbDb[ RjHdN[R RlVk[',
	    'PoTOjO RTHTO RTHjHjO RWSZW^YaZk[ R_D_Q^U[XTZ',
	    'PoSIlI RYZjZ RYOY[ RXPjPj[ RYNRU R^EXP',
	    'PofOlO RVZgZ RVRV[ RSGfGgO RVRgRg[ RlLlO R\\JXNSQ R]D[L',
	    'PoREkE RYTgT RRYlY RRER[ RYJYT RYJgJgT',
	    'PoYMlM RRTYT RRFRV RRFYFYT RcDc[',
	    'PoVKhK RROlO R_[e[ RVDVK RVDhDhK RYOWU RXShSf[',
	    'PoRJgJ RUVdV Re[l[ RUOUX RUOdOdV RRElEl[',
	    'PoRUXU RRGRX RRGXGYU R^KbTgYl[ RiIeR_XY[ R]F`R R[FjFfQ',
	    'PoRSZS RRGRV RRGZGZS RgVl[ ReOiY RbN_U[YW[ R\\EdEdMgV RdI`T RdHeT',
	    'PoRUYU Rd[i[ RRFRW RRFYFYT R`UZ[ RbP^X R\\IlIlQj[ RcCbKaU',
	    'PoRTZT Rc[i[ RRFRW RRFZFZT RgL]U R\\FlFlQj[',
	    'PoRUYU Rg[l[ RRFRW RRFYFYT R^EgEg[ RlUlZ R^E^SZZ R^M]W',
	    'PoZP`P RTZkZ RTRT[ R`J`P RTRkRj[ RRElEiG`I',
	    'Po\\FkF RRVZV R[[l[ RRGRY RRGZGZV RcFc[',
	    'Po\\NlN RRUYU RRFRW RRFYFYU ReEe[ RjD^F',
	    'Po]EkE R[NlN RRUXU R_[d[ RRFRW RRFXFYU RdEd[',
	    'PoRFlF RSNkN RTZhZ RSFSM RTRT[ R^C^G RTRhRh[',
	    'Po^IlI RRUXU R][k[ RRFRW RRFXFXU RdQ\\Y R^NiNdR R`C\\M RlWk[',
	    'PoWZgZ RWRW[ RWRgRg[ R\\CUJ RYG]LePlR RYGgGbLZPRR',
	    'PoRY\\Y R`YlY RRFR[ RRF\\F\\[ R`F`[ R`FlFl[',
	    'Po\\OfO RRTYT RdYkY RaYhY R\\ZdZ RRFRW RRFYFYT RhMeP\\Y R_K\\O RhTl[ RcD_L',
	    'Po[OlO RRUYU R_[c[ RRFRW RRFYFYU RdKd[ R\\FjFeK',
	    'PoXLfL RWZgZ RWQW[ RWQgQg[ R_CfJlM R_CWKRM',
	    'PoRGlG RTMkM RVZhZ RVRV[ R_C_M RVRhRh[',
	    'PoTKjK RfYlY RRPRY RTETK R_K_[ RTEjEjK RRPlPlX',
	    'Po[JlJ RRTYT Ra[f[ RRFRV RRFYFYT RgDg[ R^NaU',
	    'PoWIgI RYUeU Rf[l[ RRER[ RYNYW RYNeNeU RRElEl[',
	    'Po]DXIRL R[GgG_OSU R\\QmQmZ\\Z\\Q RWJ_O',
	    'PoVKlK RZZiZ RZQZ[ RZQiQi[ RVGUQTY RiD]FVG',
	    'PoSFkF RUQiQ RUJUP RUJiJiP R^U[XXYR[ RWS[W_Yj[ R_C_Q^V',
	    'Po[MkM RRUXU RY[l[ RRFRX RRFXFXU RcDc[',
	    'PoYUeU Re[l[ RRHR[ RYNYW RYNeNeU RRHlHl[ R]C[H',
	    'PoRUXU Rc[k[ RRFRW RRFXFXU RcFc[ RlWk[ RiD[G RfO\\P RkN`O',
	    'Po[ElE RRTZT RRFRW RRFZFZT RcEc[ RcKlO',
	    'PoRTXT R^[k[ RRFRW RRFXFXT R^F^[ RdCdV RkIkT RlWk[ RiJZN',
	    'PoTMjM RRZlZ RRRR[ RTETL RTEjEjL RRRlRl[',
	    'PoRUYU RRFRW RRFYFYT RdLd[ R`HaL R[D`I RlDcM',
	    'PoRTXT R[UhU Rd[i[ RRFRW RRFXFXT R]PlPj[ R^I]P R]EiEhP',
	    'Po^IkI R[QlQ RRTYT RRGRV RRGYGYT RcHc[ R_E[N',
	    'PoRSYS RRFRV RRFYFYS RfVk[ RdCcOgX RjIiP RaU^YZ[ R\\I^P RcLeU RcJbU',
	    'PoZLlL R\\SiS RRSYS RRFRU RRFYFYS R\\EiEiT RaV\\Z RbEbQaV',
	    'PoRIlI RUNiN RUEiEiN R^E\\KZPTV RYRjRjZYZYR',
	    'Po[PlP RRUYU RZ[l[ RRGRX RRGYGYU RhNhZ R\\FhFhV RaF`N`T_[',
	    'PoSFlF RWZhZ RWRW[ R_C_G RWRhRh[ RXF\\KbOkQ RgFbL\\ORQ',
	    'PoTEjE RRKlK RWZgZ RWQW[ RVRhQh[ RaKgQlS R_E[MVRRT',
	    'Po_LhL RRTYT RRFRV RRFYFYT RcDkK RcD[L R[QjQe[',
	    'Po[LlL RRTZT RRFRV RRFZFZT RgEkI RcDcLaT]YY[ RcLeTk[',
	    'Po\\McM RRTXT Re[k[ RRGRV RRGXGXT R\\D\\Z ReDe[ RjLdN RlWk[ RbYY[',
	    'PoZOeO RWZhZ RWSW[ RZGZO ReLeO RWShSh[ RgGjIlN R\\DcF RVGSN',
	    'PoRUXU Ra[h[ RRHRX RRHXHXU RaJa[ RaDgJ RjNlV RiWh[ R\\N[V',
	    'PoRSYS RRFRV RRFYFYS RgMg[ RcDkL R]WY[ RaEZL R_M_P]W',
	    'Po\\FlF RRUXU RRFRW RRFXFXU RdHd[ RfLlQ RcJ[R ReFbL',
	    'PoSElE RVZhZ RVRV[ R`G`P RVRhRh[ RcIlN RaE^IRN',
	    'Po]QkQ RRTXT R][k[ RRFRW RRFXFXT R^E^[ RdEdP R]EkEkR RlWk[',
	    'PoRTYT R\\TjT Rc[k[ RRFRV RRFYFYT R\\K\\V RdCd[ RjKjU RlXk[ RjF[H',
	    'PoRSYS Rb[g[ RRFRV RRFYFYS RhIlM R^VZ[ R]JYN R^EfEjL R^G[M R`N^W R\\NiNhUg[',
	    'PoRUXU ReZlZ RRGRX RRGXGXU R^H^[ ReDeZ RbP_Q RjHcP RlWk[ R_CZN',
	    'PoZIdI RWZgZ RWSW[ RWSgSg[ R_CbFlK RVMgMdS R_CZGRK',
	    'Po]MlM RRSYS RRFRV RRFYFYS RfMf[ RjD]F]P\\V',
	    'Po[HlH RRSYS Rg[l[ RRFRV RRFYFYS RcCcH R^LgLgZ RlWl[ R]VX[ R^L^R]W',
	    'Po\\LiL RZLgL RRSYS Rf[l[ RRFRU RRFYFYS RgOg[ RlWl[ RfGlL R^VYZ R`C\\L R_O]X',
	    'PoVF]F RVOlO RYZlZ RYSY[ RVIlIlO RYSlSl[ RVEVQTY R`E[E RjC`E',
	    'Po[IlI RRUYU RRFRW RRFYFYT RhSl[ ReO`Y R_M\\Z ReLbX R`I]X R`E^W RhY`Z RkYdY',
	    'PoZHlH RRTYT RRFRV RRFYFYS RcCcN R^O_SdXl[ R[NiNgRbWY[',
	    'Po^KeK RRTXT R^[c[ RRFRW RRFXFXT R]EeEeK RlDl[ R]K]QeRd[',
	    'PoZKhK RRTkT RZEZK RUFUPhOhT RZEhEhK R_Q]V[YS[ R`UdYl[',
	    'PoVKhK RTOjO RRTlT RVEVK RVEhEhK R`UdYk[ R_O]V[YR[',
	    'Po_RcR RRSYS RRFRV RRFYFYS RcCcR RhHlO R^GZO RhOgTcX\\[',
	    'PoRTYT Rh[l[ RRGRV RRGYGYT R\\J\\[ R\\JlJl[ RfPjT RbP^T R^DdDdKhS RdFcP',
	    'Po[PlP RRTXT RRFRW RRFXFXT R\\HjHjP RgUjY ReSiW R^YY[ RaU[Z RbDbQ_X RbLaU',
	    'PoRSYS RRFRU RRFYFYS ReLkLhTdX][ RZEfEeM R^G]R[XW[ R^KcTgYl[',
	    'PoRSYS RRFRV RRFYFYS R]HkHiN R^C[N RcIcOaV]YX[ RcOeVj[',
	    'PofLlL R[PiP RRTXT RRFRV RRFXFXT R_DgDgL R^JZN R]RbWj[ RiQcW^YX[ R_E^K',
	    'PoRTXT Rd[i[ RRFRW RRFXFXT R_PZU RdT^Z RgLbW R^DZN RcH^S RhHeT R]IlHlPj[',
	    'PoRSXS Re[k[ RZ[^[ RRFRU RRFXFXS R^J^[ ReDe[ RlWl[ RYEcE^J RcNYP',
	    'Po]JkJ R[QlQ RRTYT RRFRV RRFYFYT RdDd[ R^E\\M',
	    'PoTDjD RROlO RVZhZ RVSV[ RVIfIeO RVShSh[ R]DZO',
	    'PoVHjH RRMlM RVZhZ RVRV[ R_C_Q RVRhRh[ RXDUJ',
	    'Po\\ElE R\\OlO RRSYS Ra[g[ RRFRU RRFYFYS RgEg[ RgN`UXY R^H\\P',
	    'Po^NfN R\\ShS RRTYT Rc[i[ RRGRV RRGYGYT R`C\\K R^HlHlOlRj[',
	    'PoTLjL RRZlZ RRRR[ RTETL RTEjEjL RRRlRl[ R_M]R',
	    'Po\\ElE RfSjS RRTXT Ra[k[ RRFRW RRFXFXT RbJbZ RaJjJjR RlWk[ R\\E\\Q[X',
	    'PoeLjL RROlO RVZiZ RVTV[ R_K_T RVTiTi[ R[DZIXKSM RTDdDdHkGjL',
	    'Po[IlI RRTXT RRFRW RRFXFXT RaCcF R^LbTfXl[ RgLdT`XY[ R^IaS RhIeT',
	    'PoVLhL RSQkQ RVDVK R_L_[ RVDhDhK R_PeVlY R_PYVRY',
	    'PoSH\\H R[P_P RVZhZ RVRV[ R_C_O RVRhRh[ R_DaHeMlO R[IWNRO RjFbJ',
	    'PoVLhL RTPkP RUViV RR[l[ RVEVL R_P_[ RVEhEhL',
	    'PoZKhK RRUlU RZDZK RUFUPgPgU RZDhDhK RcXkZ RZXR[',
	    'PoVHjH RRNlN RVYhY RVRV[ R`C`N RVRhRh[ RXDUJ',
	    'Po[IjI R[PlP RRTXT RRGRV RRGXGXT RdTjZ RbCbPfW R`T\\YX[ RbLaT',
	    'PoRSZS RRFRU RRFZFZS RgCg[ R]MbO R^FdH RgS\\U RlSbT',
	    'Po]EjE R[MlM RRSYS RaYkY R^YhY R[ZeZ RRFRV RRFYFYS RgTiVk[ RbM^Y',
	    'Po^NjN RRSYS RRFRU RRFYFZS R^EjEjN RfVl[ R\\WX[ ReQhY RdNgW R^E^P\\V',
	    'PoZNlN RRSYS RRFRV RRFYFYS RgDg[ R\\ZZ[ RcE[G R`F`Q^W',
	    'PoRUXU Rg[l[ RRFRW RRFXFXT R]J][ R]JlJl[ RhQjU ReCdMhQ ReGcQ',
	    'PoRKlK RXZgZ RXPX[ RWQhQg[ RbKhQlS RTFYF^F[MVRRT RgD[F',
	    'Po\\EjE RZMlM RRTXT Rd[l[ RRFRV RRFXFXT RdMdZ R_UY[ RlWlZ RaO]X RbEbJ`T',
	    'Po[GlG RRUXU R][k[ RRFRW RRFXFYU R`D`K RhDhK RlWk[ R]NiN]Y',
	    'Po[KlK RRUXU RRFRW RRFXFYT RgVl[ R`WdZ R`UZ[ RdNhX RbO^W RcCcLfU RcFaS',
	    'Po\\ElE RRTYT R\\YlY RRFRV RRFYFYT R\\E\\[ R`KcMeNjT RdQ_V RhHdR',
	    'Po\\ElE RRTXT Rd[h[ RRFRW RRFXFXT R`Z^Z R_LkLkRi[ R\\E\\QZY RdGdNbW',
	    'PoRTXT RRFRW RRFXFXT R]D]U R]DjDjU RhXk[ ReWiX RbU_YZ[ RdHdObV',
	    'PoVJhJ RUNUW RVEVI RVEhEhI RUNiNiW RaWl[ R_U\\XR[ R_Q^V',
	    'PoTKiK Rf[l[ RROR[ RTETJ RTEiEiJ RROlOl[ R^QhV R^Q[TUW R_K^R',
	    'Po^IlI RRUYU RRFRW RRFYFYT R^JbTgYl[ R_E[O RhJdS`XY[',
	    'PoaUfU RRUXU R^[i[ RRFRW RRFXFXU R^M^[ RbDjK RbDZL RjWi[ R]MgMfU',
	    'PoeMiM RRTYT R[UiU Rf[k[ RRFRV RRFYFYT RcCbF R^FjFiM R^F^PlQlVk[',
	    'Po\\FcF R\\MkM RRTXT R_ZlZ RRGRV RRGXGXT R\\HkHkM RdT^Y RfRcU R_PiPeS RlWlZ R\\E\\QZY RjDaE',
	    'Po\\IhI RRSYS R^UfU Rg[l[ RRFRV RRFYFYS R^M^W R^MfMfT R\\ElEl[',
	    'Po[HlH RRSYS RRFRV RRFYFYS RiVk[ RcDcH R]ObR RhL[Z RZZkY',
	    'PoZJkJ RRTXT R]VhV RRGRV RRGXGXS RbCb[ RfQlV R^QXV RcJgR RaJ]R RbH_P',
	    'Po\\JjJ R\\PlP RRTXT RRFRV RRFXFXT R\\D\\[ R\\DjDjJ RlWk[ RgYj[ RcY[Z RdKeW',
	    'Po\\KkK RRUXU Ra[k[ RRFRW RRFXFXU RbMb[ R\\EkEkK RlWl[ RkOaT R\\E\\Q[X',
	    'PoZNlN RRTXT R\\WlW Rc[h[ RRFRW RRFXFXT RaQdT RaHeK R^FjEjSh[ R]F]L\\P\\W',
	    'Po^LhL RRTYT ReXiX RRFRV RRFYFYT RaPa[ R[PiPiX RcCkK RbD[L',
	    'Po\\NjN RZTlT RRTXT Rf[j[ RRFRV RRFXFXT R\\H\\[ RcCcS R\\HjHj[',
	    'PoRTXT RRFRV RRFXFXT RZL^P R`RbX ReRaZ R^DZL Rj[f[ R`K\\SZW`VcV RbIlIlTj[ RfCfLdV',
	    'Po]MlM RRUXU R[[g[ RRFRW RRFXFXT R]F]W RlVkZiZ RjD]F RdVZW ReEeKgX',
	    'PoZIhI RYMiM R[WgW Rf[l[ R[Q[Y RaFaM R[QgQgW RVElEl[ RVEVPTX',
	    'Po]EjE R[LlL RRTYT RRFRV RRFYFYS RhPlX R^P[W RcLc[_[',
	    'Po\\OjO RRSYS Rf[l[ RRFRU RRFYFYS R\\E\\O RfOf[ R\\EjEjO RlWl[ R`O^W[ZX[',
	    'PocIlI RRTZT R\\ZjZ RRFRV RRFZFZT R\\P\\[ RcCcP R\\PjPj[',
	    'PofQlQ RRSYS R\\YlY RRFRU RRFYFYS R\\E\\[ RgEgQ R\\ElEl[ RbEbLaQ',
	    'PoZKlK RRSXS RcTiT R]YkY RRFRV RRFXFXS R]D][ RcDcT RiDiT',
	    'Po]QjQ RRTYT RRFRV RRFYFYT R]E]Q R]EjEjQ RjWk[ RfTjX R_V[[',
	    'Po[EkE R[QlQ RRTXT RRFRW RRFXFXT RcEc[ R^H^N RiHhN',
	    'PoYH_H RbNkN RWZhZ RTFTO RWRW[ RYCYN RbCbN RWRhRh[ RlKkN RjFaH R^MRO',
	    'PoRSXS R`YgY R\\YcY RRFRV RRFXFXS RbGbY R\\E[RWZ RfTg[ RiQk[ RhDhMjY R[LZV RiD]F',
	    'Po_McM RRTWT Re[l[ RRFRW RRFWFWT R[J[[ R_D_Y RfDf[ RkIeO RcXXZ RlWl[',
	    'Po\\HjH RZOlO RRUXU RRFRW RRFXFXT RcCc[ RcO`TYZ RcOfTlY',
	    'PoRSYS R^UfU Rd[i[ RRFRV RRFYFYS R^M^W R^MfMfU R`C\\L R^IlHlRj[',
	    'Po[ElE R]TdT RRUXU Rc[i[ RRFRW RRFXFXT R]J]U R]JdJdS RiEi[',
	    'PoRSWS RRFRU RRFWFWS RhWl[ R^TbW RgU`[ R_UX[ ReOiY RZR`U RjMeW R`O\\X RkHgU RcGfU RbFkFhR R]CYS RYIaI_U',
	    'Po\\LlL R\\SlS RRUXU RRFRW RRFXFXT R\\E\\T RdEd[ R\\ElElT',
	    'Po[FlF RRTXT RZ[l[ RRFRW RRFXFXT RdIdY ReLlR RcK[S ReFbL',
	    'Po]IjI RZPlP RRTXT RRGRV RRGXGXT ReUk[ RcCcPgX RcP`V]YX[ R^E[M RcLaU',
	    'Po]KjK R[OlO RRUYU Rc[h[ RRFRW RRFYFYU R]E]K R]EjEjK R_O]TjTi[',
	    'Po\\MlM R\\SlS RRTXT RRFRW RRFXFXT R\\G\\U RdCd[ R\\GlGlU',
	    'PoZQlQ RRSWS R^[c[ RRFRV RRFWFWS RcEc[ R]H^N RjHgO RjD\\F',
	    'PoUW]W RdXiX RUNUY RUN]N]V RaNa[ RaNiNiX R_CXIRL R_CeImL RZKdK',
	    'Po]NiN RRUYU R]ViV RY[l[ RRGRX RRGYGYU R]F]V R]FiFiV',
	    'Po\\IkI R[OlO RRSYS RcYkY R]YgY R[ZdZ RRFRU RRFYFYS RgTjWk[ RcCcO]Y',
	    'Po]LiL R]SiS RRUXU RY[l[ RRFRW RRFXFXU R]E][ R]EiEi[',
	    'PoZIlI R^QhQ RRTYT R^YhY RRGRV RRGYGYT R^C^[ RiCi[',
	    'Po[ElE RgUjU RRVWV R[[l[ RRFRX RRFWFWU R[E[[ R_K_U ReHeX R_KjKjU',
	    'PoRTXT Rb[k[ RRFRV RRFXFXT R]G][ RbFbZ RfCfV RlWk[ R^CZN RhK^M RkJ`L RkIkShS',
	    'Po]NlN RRSYS R\\YlY RRFRV RRFYFYS R\\Q\\[ R]F]N RdCdY RkFkN RlQl[',
	    'PoTFjF RRNlN RWZgZ RWRW[ R_C_G RWRhRh[ RXHZM ReHcM',
	    'Po^SeS RRTXT RfUiU R^[k[ RRFRW RRFXFXT R^K^[ R^KeKeS R^GjGjT RlXk[ R_C\\L',
	    'PoRTXT Ra[i[ RRGRW RRGXGXT RaIa[ RjWi[ RdQ[Z R`DdF RiEcR RjMlT R\\M[U',
	    'Po[MkM RRTXT RhXkX RRGRV RRGXGXS RfCf[ R[GkGkM R_V[[ R[RlRkX R[MZS R`CaQ_W',
	    'PoZQlQ RRSXS RRFRV RRFXFXS R]H]Q R]HjHjQ ReUjZ RcDcQfX RbT^YY[ RcLbU',
	    'Po[MkM RRSYS RRFRV RRFYFYS RcEc[ RfSlX R`RYY RcKgT RcK_R RiD\\F',
	    'Po^HlH RbMkM RbTlT RRTYT RRFRW RRFYFYT RbHb[ R_C\\L',
	    'PoSK`K RbXlX RZEZ[ RbFbZ RbFlFlZ RYL_R R_DSE RZLVRRV',
	    'Po[MkM RRTYT R^YjY RRFRW RRFYFYT R^Q^[ R^QjQj[ RgHkM RbD]M',
	    'Po_OlO RWZhZ RWRW[ RWRhRh[ RdGjM RWFRK RcI_M ReDbK RWG^F\\KXORR RXCVHWJ[NeO',
	    'PoRTXT R^[b[ RRGRW RRGXGXT R[IbIb[ ReSkX R]SXY RjKfP RbLfT RZN_N\\V RbIeR R^DfF',
	    'Po`JlJ RRTXT Rd[i[ RRFRV RRFXFXT R^G^[ RiCi[ RbNdT R`C[M',
	    'PoaElE RZIaI RRSXS Rd[h[ RRFRU RRFXFXS R^C^[ RhEh[ RaPYR RZ[^[Z[',
	    'PoTK\\K RaKjK Re[k[ RTETK RTE\\E\\K RaEaK RYOeOe[ RaEjEjK RlWl[ RYOXVUZR[',
	    'PoZElE RRTXT Rg[l[ RRFRW RRFXFXT R]R\\[ R^O^W R^M^U R^J^R R^E^N R^KgKh[ RlWl[ RaOcT ReY[Z',
	    'PobHjH RZNlN RRUXU RRFRW RRFXFXT RbCb[ RdSkV',
	    'Po\\JlJ RRTYT R^ZkZ RRFRV RRFYFYT R^Q^[ RdCdQ R^QkQk[',
	    'PoRTXT ReXlX RRERW RREXEXT ReGeZ ReGlGlZ RaZ]Z R^C^M\\W RZIcIbSaZ',
	    'Po[IlI R`QkQ RRTYT R][l[ RRGRW RRGYGYT ReQe[ R]TYX RaK[W RbD^S',
	    'Po[GlG RRTXT RRFRW RRFXFXT R_L_X RdJd[ RaE\\P R]NjNjXhX',
	    'PoZJlJ RRUXU Rd[l[ RRFRW RRFXFXU ReJe[ RlXl[ ReEiF R\\VX[ RiP`Y RiNhR R^Q[Y R_C_K]U',
	    'PoRUXU RRFRX RRFXFXU R`DZK R^HdNlQ RaRfT R^GhGbMZQ R]Wi[',
	    'Po\\NlN RRVXV R`[d[ RRGRY RRGXGXV RdNd[ RcCdF R\\H\\L R\\HlHlL',
	    'PoRSYS R_[k[ RRFRU RRFYFYS R`K`[ RlWk[ R\\GlGlK R\\G\\K RdCdG RiO_R',
	    'PoZN`N RRUXU RdVlV RZVaV RY[l[ RRFRX RRFXFXU R]RZV RgPdV RjJfT R_DZN RaJ]S RhDcN RiMcN',
	    'Po]ElE R\\OlO RRUYU RRFRW RRFYFYT RhEh[ RaE`S]Z R`N_W',
	    'Po[ElE R]RjR RRTYT R]ZjZ RRGRV RRGYGYT R]K][ R]KjKj[ RcEbK',
	    'PoVJiJ RRMlM RWPgP RWTgT RaXlX R^XjX RSYbY RVDVJ RWMWY RgMg[ RVDiDiJ',
	    'Po[ElE R^JiJ R^PiP RRTYT RRFRV RRFYFYT R^E^W RiEi[ RbVZW RdV]W RgV_W RiUbV RlUeV',
	    'PoTJ]J RaJjJ RVMiM RRQlQ R_[e[ RTDTJ RTD]D]J RaDaJ RaDjDjJ RYQWUgUe[',
	    'Po[NlN RRUXU Rf[l[ RRERX RREXEXU RcCcN RfNf[ RlWk[ R_UY[ R\\F_K RiEgK R`N_V',
	    'PoRTYT Rb[k[ RRGRW RRGYGYT RbLb[ RkXk[ RcCcG RhJ\\M R[G[K R[GkGkK RcR[S RfR_S RjQaR',
	    'Po[ElE RZNkN R\\TkT RRTYT RY[l[ RRFRW RRFYFYT RcPc[ RfIlO RaE]N',
	    'PoZFlF R\\OiO RRSXS RRFRV RRFXFXS R[JiJiN RcCcSdVgYl[ RbRaV^XX[ R[N[SkRjWhV RbNcU',
	    'PoZDdD RRSXS Rh[l[ RRERU RREXEXS RfGfU RlCl[ R]N`R R_UY[ R]IYQ RbN^X R^DZP R\\JcJ_U',
	    'PoSFZF RVZhZ RVRV[ RVRhRh[ RcGeMgOlQ R^GkGiK R_C\\J RcGaL_NYP R[MRN',
	    'Po[IkI R\\NjN RRSYSlT RRFRU RRFYFYR RcIc[ R^C_G RiDfG',
	    'PoZNlN RRSXS RRFRV RRFXFXS RcCc[ ReSlY R`RYY R\\E^K RcLfT RcL_S RiEfK',
	    'PoaNjN RUN^N Rc[l[ RUE^E^N RaEaN RaEjEjN RiRkV RbS_V RcPaU RYO\\V`Yi[ RUEUQS[',
	    'PoZGlG RRSYS RRFRV RRFYFYS RcCcG RgJkN R_JZO R_OaSeXl[ RgNfSaWY[',
	    'Po[HlH R\\NkN RRUXU R]ZjZ RRFRW RRFXFXT R]R][ RdCdN R]RjRj[',
	    'Po]IjI R[PlP RRTYT RRFRV RRFYFYS RcCc[ ReSlY RaRZZ RcNeS RcNaS R^E\\M',
	    'Po_GiG RRTYT R]ZjZ RRFRV RRFYFYS R]R][ R]RjRj[ R`E[K R_IdOlR RhHcNZR',
	    'Po^QfQ RRTYT R^UfU Rd[j[ RRGRV RRGYGYT R^L^V R^LfLfU R_C\\K R]HlHlSj[',
	    'Po]MlM R]SlS RRUYU R]YlY RRFRW RRFYFYT R]G][ R]GlGl[ RdCbG',
	    'Po\\IkI R[PlP RRSYS RRFRV RRFYFYS R]D_H RiCgH RcJcPaV^YY[ RcQeVj[',
	    'PoZHlH RRSYS RRFRV RRFYFYS RhXk[ RdVhW RgKaR RcCcH]N R[M^NaQZT RjPeVY[',
	    'PoZHlH RRTWT RRGRW RRGWGWT RfVk[ RdV_[ ReQhY RkMiS R^MZV RbM`S R`C]R RfJdV',
	    'Po[GkG R_MhM R\\QjQ RRTXT Rb[g[ RRFRW RRFXFXT ReGkM RbG[M RcC`J R`Q^UhUg[',
	    'PoZLlL RRUXU R\\ZjZ RRHRW RRHXHXU R\\R\\[ RdEdR R\\RjRj[ RiD[F',
	    'PoRTXT Re[l[ RRFRV RRFXFXT ReCeZ RlXl[ R[H]L RgOkT RjHgL R_VZ[ R`P\\RZS R`C`Q_W',
	    'PoUHlH RXL`L RXV`V RXPXX RXP`P`V RfS_Z RgZj[ ReDjF RiLeU RlVk[ RdCcLeX RUHUSSZ',
	    'PoZNlN RRSYS RRFRV RRFYFYS R[G[K R[GlGlK R]Tk[ RbJ\\U RcCcG RiNfUcX`YZ[',
	    'Po[EkE R^PiP RRUYU R^ViV RY[l[ RRGRW RRGYGYU R^J^V R^JiJiV',
	    'Po^JlJ RRTXT RRFRW RRFXFXT R]G][ ReCe[ ReJjV R^DYO RdL_V ReHhR ReHbR R]E\\L',
	    'Po^IhI RYXeX Rf[l[ RRNR[ RVEVN RYRYZ R^I^N RYReReX RVEhEhN RRNlNl[',
	    'Po^LjL RRTYT R\\YlY RRFRV RRFYFYT R\\E\\[ R\\ElEl[ RdPjV RdHdLdP^V',
	    'Po[HgH RZMlM RRTYT R`[k[ RRFRV RRFYFYS R`Q`[ RaCaM RkXk[ RjG`QXU RiR_U',
	    'Po_KlK R`RjR RRSWS RRFRU RRFWFWS R]G][ RaEjEjS R^CYN RdEdQcX',
	    'PoSGkG RVPgP RVJVP RZTZZ R_C_G RVJgJhP R`PaTeXl[ R^QSV RjRbU RbYU[',
	    'PoWLgL RRZ\\Z RaZlZ RRPR[ RWDWL RRP\\P\\[ RaPa[ RWDgDgL RaPlPl[',
	    'Po[ElE RfRlR RRTXT R\\YlY RRFRW RRFXFXT R\\J\\[ RaEaP RfEfR R\\JlJl[ R_R]S',
	    'Po_IiI RRTYT RaUhU Rg[l[ RRFRV RRFYFYT R]E][ RaMaV RaMhMhU R]ElEl[',
	    'Po[JlJ RZSlS RRUXU RRFRW RRFXFXT R_D_R RgDgR RgVl[ R_VZ[',
	    'PoRTXT R\\VhV Re[j[ RRFRW RRFXFXT R\\M\\V RhMhV R_LdQ ReS R`D\\J RdK`T R^HlGlTj[',
	    'PobPlP RRSYS RRFRV RRFYFYS RkQeW RcC_H[J R^HaIeL\\P R`GiGdMaQ[T RaSfVbYZ[',
	    'Po\\HjH R[NkN R[TjT RRUXU RY[l[ RRFRX RRFXFXU RcDcN RcPc[',
	    'Po^LhL RRSYS R]ZjZ RRFRV RRFYFYS R]Q][ R]QjQj[ RcDlL RbE[L',
	    'PoSG_G RRLlL RTX^X RTPTZ RYCYL RTP^P^W RgEjH RbY^[ RhR`Z RgZj[ RiNgT RcOfY RlWk[ RcCcKeW',
	    'Po[GlG R_PjP RRTYT R_UjU Rf[j[ RRFRV RRFYFYT R_J_[ R^MjLj[ R_LZP RaE\\O',
	    'PoXL_L RRUWU Rb[k[ RRGRW RRGWGWT R\\E\\W RbFb[ RfCfV RkIkT RlWk[ RkI_N R_VXX',
	    'PoRTXT RRFRV RRFXFXT R`J`Z RhVlZ RjMfPiY ReY_Z RdJgV RiI`J R\\F\\QZY RiC\\E',
	    'PoRTXT RaUgU Rh[l[ RRFRW RRFXFXT R\\H\\[ RaMaV RaMgMgU R\\HlHl[ RdCbH',
	    'PoZGlG RRSXS RRFRU RRFXFXS R_C_J RfCfJ R]L`SeXl[ RhLeS`XY[',
	    'Po\\JiJ R\\OiO RRTXT RRFRV RRFXFXT R\\E\\[ R\\EiEiO RaOcUgYl[ RjRdU RcY[Z',
	    'Po\\ElE R_JjJ R`PjP RRVXV R_VkV R\\[l[ RRFRX RRFXFXV R\\E\\[ ReJeV',
	    'Po[FkF RRUXU RY[l[ RRFRX RRFXFXU R`F`[ RfFf[ R[L]T RkLiU',
	    'Po`JlJ RRUWU Ra[l[ RRFRX RRFWFWU R[E\\I RYM]M]XZ[ RfCfLkW ReNbV RfGdR',
	    'PodGkG R`GhG RZRlR RRTXT Re[l[ RRFRW RRFXFXT RfRf[ RkLkO RlXl[ R_W\\[ R[HaFbKeNiO R`CbK R`R_Y RhJcL[N',
	    'Po\\HbH ReNlN RRSWS RZTkT RRFRV RRFWFWS R\\C\\O RcPc[ ReCeN RlLlO RkFdI RbM[N',
	    'Po]JlJ RRUYU RRFRW RRFYFYT R]E]J ReCeJ RlElJ RbM\\S RbSfV^Z RbJ`PlOjSdX[[',
	    'Po^HkH R^MkM RRUXU R[UlU RRFRW RRFXFXT R^M^U ReGe[ R`C\\K',
	    'Po[TlT RRUXU RRFRV RRFXFXT R^F^P RcQc[ R]I[K ReCeOkNlO R_C\\J RjEgI`L',
	    'PobEkE RaLlL RRUXU Rd[h[ RRERW RREXEXU R^L^[ RiLi[ R_DZI R_I^LYP',
	    'Po^MhM R\\QkQ RRTXT RdZjZ R\\ZcZ RRFRW RRFXFXT RfUkZ RcDiJlL R`T_W[Z RcE[M RaR_V ReY`Z',
	    'PofLkL R[QkQ RRTXT RRFRW RRFXFXT RcMc[ R_DfDgK R]WYX RcQ\\X R_J\\M RcQjXlX R_E^K',
	    'PoTL\\L RaMkM R_[d[ RTETL RTE\\E\\L RaDaM RkJkM RcPkS RZPRS R]UZYT[ RjFaH RWSfSe[',
	    'Po[GlG RRTXT RRFRW RRFXFXT RgQg[ R_W[[ RcCdG R_HcMfNlP RhHdMaNZP R_Q^X',
	    'PoRUWU RRFRW RRFWFXU R[H[K R_O_[ R[HlHlK RgUlZ R`MZT RiPeR RkNhQ RdNhX RcF_Q RcIfU RdDaM ReY][',
	    'Po_JjJ R\\JiJ R\\PkP RRTXT RZUlU RRFRW RRFXFXT RdKd[ RfEkJ RbC\\J R^L[S',
	    'PoRTWT Rf[j[ RRFRW RRFWFWT R^OZS ReNhS R^DZM R`J]P ReDcM RbWYY R_LZM RdIlIlTj[ RbSZS',
	    'Po^HjH R[NlN RRTYT R]ZjZ RRFRV RRFYFYT R]R][ RcCcP R]RjRj[ R_D\\K',
	    'PoWIhI RUPiP RUSiS RUWiW RULUW RWDWI RWDhDhI RULiLiW RdXl[ R[XR[',
	    'Po[EkE R\\JjJ RZNlN RRSYSlT RRFRU RRFYFYS RcEcN RgPg[ R^WZZ R_Q^X',
	    'Po[ElE R]QjQ RRTXT RZ[l[ RRFRW RRFXFXT R]J]Q R]JjJjQ R^T`Y RhSd[',
	    'Po[JkJ RRUYU RcZkZ RRFRW RRFYFYU RcJcZ RfEiG R\\WX[ RlVlZ R^RZZ RiLcO RjPcS R_C_N\\X RkSdW^X R_H]U',
	    'PoSElE RVL`L RbNgN RRPlP RVX`X Rb[g[ RVSVY RVHVL RVH`H`L RVS`S`X RgEgN RgPg[',
	    'PoYLlL RRTXT RZ[^[ RRERW RREXEXT R^E^[ RhDkG RhSaZ RjNfV RlWk[i[ RbDYF RcQYS ReCeNgY',
	    'Po\\GjG RZMlM RRTXT Rb[e[ RRGRV RRGXGXT RcCcM RfMf[ R_W[[ RiPlV R\\PZW RaM_X',
	    'Po]PjP RRTYT R]UjU Rf[j[ RRFRV RRFYFYT R]L][ RcCcK R]LjLj[ RgElJ R_F[J',
	    'Po\\KkK R\\QkQ RRTXT R[UkU RY[l[ RRFRW RRFXFXT R\\E\\Q RcEc[ R\\EkEkQ',
	    'PoZKcK RZRcR RRTWT RRERV RREWEWT ReEe[ ReElEiM R]E]SY[ RkWhW RYEbEaZ][ RjOlTkW R]L[W',
	    'Po]LjL RcRkR RRTXT Rc[l[ RRFRW RRFXFXT R]E]L RdLdZ R]EjEjL R]Q[Y R]S^WaZh[',
	    'Po`SfS RRUWU Re[j[ RRERW RREWEWT R]G][ R`K`U R`KfKfS R_EkEj[ R\\JWN R^CZM',
	    'PoTK\\K RaKjK RRQlQ RTETK RTE\\E]K RaEaK RaEjEjK RdMiO R`QaUdXk[ R]TZXS[ R_M]V',
	    'Po\\GhG RZLlL R]PiP RRSYS R[UlU R^[c[ RRFRU RRFYFYS RbCbK RcSc[ RjFaP^QWU RhPcR',
	    'Po]IiI R]MiM R]RiR RRSYS Re[l[ RRFRV RRFYFYS R]D]R RfRf[ R]DiDiR RlWl[ R_V]YX[ R`R^X',
	    'Po]OjO RZTlT RRTXT RRFRW RRFXFXT R[G[L RcJc[ R[GlGlL RbCbF R^K\\P',
	    'Po\\FkF R^OiO RRTYT R[TlT R_[c[ RRFRV RRFYFYT R[K[N RcCcK RcRc[ R[KlKlN RgPcR',
	    'PoRG]G R`JlJ RVZhZ RVSV[ RgJgQ RVShSh[ RXCXQTQ R`E`J_N RjD_E R]JRL',
	    'PoYI`I RbLlL RRSXS RZ[][ RRFRU RRFXFXS R]C][ RhLh[ R`PYR RkDbFbSaY',
	    'Po]KjK R]PjP RRTXT RRFRV RRFXFXT R]F][ R]FjFjP RdTjZ RcPeW RcCdF RkSeU RcY\\[',
	    'Po\\LcL RRSXS R_[b[ Rh[l[ RRERV RREXEXS R\\D\\L R\\DcDcL RgFgT RlCl[ R_M]X R[QcQb[',
	    'Po[GaG RfGlG R\\LkL R[RlR RRTYT RRFRV RRFYFYT RaCaJ RcLc[ RfCfJ',
	    'Po[QlQ RRTXT Rc[h[ RRFRW RRFXFXT RhNh[ R^SbW R\\H^L RcGdL RkFhL R`E[F RjD_E',
	    'Po[FaF RZPlP RRTXT RRFRV RRFXFXT R\\H]L RbGcK R^Uk[ RiGgL RaL^U RhPfUcX_YY[ RjD`E',
	    'Po]MjM R[SlS RRUYU R`[c[ RRFRW RRFYFYU RdQd[ R\\G^K RcGcK RiFgK RhOdP RjC^E',
	    'Po[HlH R\\QkQ RRSYS R\\UkU Rg[k[ RRFRV RRFYFYS R\\L\\[ RcCc[ R\\LkLk[ RgDkF',
	    'Po\\HiH R\\LiL R[PjP RRSXS RZUlU RRFRV RRFXFXS R\\D\\L RcPc[ R\\DiDiL',
	    'Po[FlF R]NiN RRSXS R^[c[ RRFRV RRFXFYS R]I]N RcCcG RcTc[ R]IiIiN R\\QjQcT',
	    'Po[ElE R\\MkM R\\QkQ RRTXT RRFRW RRFXFXT R\\H\\Q R\\HkHkQ RbU^Y R\\S`XY[ RcEcS`XfZl[',
	    'Po`EgE R]RiR RRSXS Re[k[ RRGRU RRGXGYS R]K]Q RfRf[ R]KiKiQ RkWk[ RgFlJ R_FZK R_W]ZY[ R`R^X',
	    'PobMkM RYM]M RRPlP RVX`X Rb[g[ RVSVZ RVS`S`X RbFbM RgPg[ RbFkFkM RWJSN RXDVK RRF^G]M',
	    'PoZIlI RRSXS Rd[i[ RRFRU RRFXFXS RdEdO R_WZ[ RcHgMlO RcH^NZO R`Q^X R[QfQeUjTi[ RiD]E',
	    'Po]EjE R[HlH R]LjL R]PjP RRTYT R]ZjZ RRFRW RRFYFYT R]T][ R]TjTj[',
	    'PoRUXU R^ZiZ RRFRW RRFXFXT R^R^[ R^RiRi[ RdKkR RdHfM RfDlI RcK[R R`E\\J RcHaM',
	    'Po]OlO R]TlT RRUYU Rg[l[ RRHRW RRHYHYU ReJeS R\\KlJl[ R_G[K R`C]J]UZ[ R`FgFdL R]O\\Y',
	    'Po]JjJ R]PjP RRSYS R]UjU RRFRV RRFYFYS R]E]U R]EjEjU RgWl[ R_XY[',
	    'Po^JgJ RRSYS R\\ZiZ RRFRV RRFXFXS R\\S\\[ R\\SiSi[ RcChIlJ R]IZK RbC\\J R\\NhNfS',
	    'PoZIkI RRSXS RRFRU RRFXFXS ReIeNkNkM RgFkJ RaO[U R`C[I R_I^MZO R^SbWfYl[ R^RhRdW`YX[',
	    'PoUEjE RXHhH RULlL RZQ`Q RWZhZ RWSW[ RZLZQ RWShSh[ R`LcPgQlR RiMcO RUEUKTQ',
	    'Po]JjJ R\\TkT RRTXT R\\[k[ RRFRW RRFXFXT R]M][ R]E]J RcMcT R]EjEjJ R\\MkMkU RlXk[',
	    'Po[JjJ R]OjO RRRXR RZSlS RRFRU RRFXFXR RfFkK RbRdVgYl[ R`D^J R^K[Q RbU^YZ[ RcNbV',
	    'Po[HkH RRTXT RRFRW RRFXFXT R]N`Q RdUjZ RhNlR RcCcQfX RhKeS R^LZS RaU]YY[ RcLaU',
	    'Po^LhL RZQlQ RRTXT R_[c[ RRFRV RRFXFXT RcLc[ RgTkX R]VZY R^IZK RcC\\J RcDgIlJ',
	    'PofMkM RRSWS RbVkV RaZlZ RRFRU RRFWFWS RbJbV RgEgV R\\VX[ R^Q[Y RYE^EZN_N\\X RkDaF RZQ\\V_YgZ',
	    'Po^KiK R[PlP RRSXS Rf[l[ RRFRV RRFXFXS R[G[J RcCcG RfPf[ R[GkGkJ RlWk[ R_V[[ RaP^X',
	    'PoZLdL RRSXS Rh[l[ RRFRU RRFXFXS R`E`[ RfFfU RlDl[ R_NdS R\\SXW R_KZV RcDZF',
	    'Po[LlL RRTXT RgYjY RRFRW RRFXFXT R^P^Y RdNd[ R\\RjQjX R`MZS RaI^O R\\DkI RiCbGZI',
	    'PoTFlF RVMlM RYQiQ RYZiZ RYTY[ R`C`F RaGaQ RYIiIiQ RYTiTi[ RTFTSS[',
	    'PoRTXT R[TjT RZ[l[ RRFRW RRFXFXT RcCc[ R]J`Q RhKjNkQ R^G[P RiFfP',
	    'Po[ElE RfQkQ R_ThT RRUXU R\\YkY RRFRW RRFXFXU R\\J\\[ RaEaO RfEfQ R\\JkJk[',
	    'Po[GlG R\\NjN R\\SjS RRUXU RZVlV RRGRV RRGXGYT R\\J\\R RcCc[ R\\JjJjR',
	    'Po[DkD R[OlO RRTXT R]ZjZ RRFRV RRFXFXT R]S][ R\\IiIiO R]SjSj[ RaE_O',
	    'Po]KjK R]PjP RRSYS RZUlU RRFRV RRFYFYS R]F]P RcCbF RcPc[ R]FjFjP',
	    'PoZElE RRTXT R[UkU RX[l[ RRGRV RRGXGXT R[K[U R`E`[ ReEe[ R[KkKkU',
	    'Po\\HjH R[MlM RcSkS RRTXT Rb[l[ RRGRV RRGXGXT RcCcZ R]SZZ R]Q[Y R\\U`Zh[',
	    'PoSE_E RVO^O RWZgZ RVHVO RWRW[ RUJ^J^O RWRhRh[ RWHRL RbFdKhNlP RWEUJ RaEkEiJeM`O',
	    'PoRVWV RRGRW RRGWGWU R_K_Y RcW_[ R_D^GYL R]OYS RfCfRkZ ReQbY RkIiN R[D^L[R RbIaO RfNcX R][Z[ R]G_R RfIhV',
	    'PoYHlH R\\LiL RRUXU RRHRW RRHXHXU RZOZR R\\EiEiL RZOjOjR R^SbXi[ R\\RhRdW\\[',
	    'Po[FkF R\\JiJ RZNkN RRTXT RRFRW RRFXFXT RcCcN R`PZV R_RdXl[ RaN_RiRdW[[',
	    'PoRFlF RTJjJ RTY\\Y Ra[k[ RTRT[ RTR\\R\\Y R_C_O RaQaZ RkXkZ RaKkP R_I]KRP RjSaU',
	    'Po\\EkE RZJlJ RRSXS RRFRV RRFXFXS R\\M\\U RcEcJ R\\MjMjU ReWkY RcV`YY[ RcQbW',
	    'PoZIaI RRSXS RZ[^[ RRERV RREXEXS R^C^[ RiCi[ RbMeP RcFgJ RaPZR RkS`U',
	    'PoUFiF RRJlJ RTY\\Y Ra[k[ RTRT[ RTR\\R\\Y R_C_J RaRa[ RcLgM RVO_OgOjP RlXk[ R[JWN RjSaV',
	    'PoZFlF RRTXT Rc[h[ RRFRW RRFXFXT R[L[O R`C`I RgCgI R[LlLlO RaU^ZZ[ RcO`W R\\RjRh[',
	    'Po[ElE RRSXS Rg[k[ RRERV RREXEXS R[K[[ R[KkKk[ RaEaOcS RfEfOiT RaJ_T RfKdU',
	    'PoRUXU RRFRW RRFXFXT R\\K\\W RcCcK R\\KiKiW R\\E_H RjEfI RdWl[ RcNbU_YZ[',
	    'Po\\KjK R\\OjO ReTkT RZU`U RRUXU Ra[k[ RRGRX RRGXGXU R\\F\\O RaOa[ RcCbF R\\FjFjO RlWk[ RdT^T RgT`T',
	    'PoZRlR RRUWU RRGRW RRGWGWT R\\I\\R R[JiJiR R_EZJ R^FhFcJcRdUhYl[ RbT]YX[',
	    'PoRSWS RRERV RREWEXS R`TeXf[ RcKfM ReFlM RdDfH RhSdX RcF\\M RZD\\I R_PjPgU R\\QYY RdCbH',
	    'PoRTWT RRERW RREWEWT RfCfS R[D_E RiHlN RZK]M R]RYZ RjPhUeY_[ RbH_P',
	    'Po[JbJ R[ObO RRTXT RhVlV RRERV RREXEXS R[E[X R[EbEbO RfEf[ RfElElV R`RcY RbWYX',
	    'PobLjL RbSjS RRTWT R`ZlZ RRFRW RRFWFWT RZDZ[ RbEbZ RbEjEjZ R]L`R RZE`D]L R_U\\U',
	    'Po[IlI R[RlR RRTXT RRFRV RRFXFXT RdEdO RcHgLlN RaP`T RcH_L[N R^WjZ RgTdY[[ RjD_E',
	    'Po[FkF R\\JjJ RZNlN RRSXS R^ShS R[VkV RRFRU RRFXFXR RcPc[ RfNlT R`NYT ReJhP RbH]Q RbC_O',
	    'Po]MiM R[RkR RRTYT RZ[l[ RRFRW RRFYFYT RcMc[ RcCkK RbD[L R]T_Y RiTfY',
	    'PocFkF RbQkQ RRTXT Rf[l[ RRFRV RRFXFXT RcKcP RcCcI RaTfTg[ RlXl[ R[IkIkL R_Y\\[ RaT_Z R[I[SZY RhMcM]N',
	    'Po[GlG R_LhL R_PhP RRTXT R[TlT RRFRW RRFXFXT R_C_T RiCiT RgVl[ R`V[[',
	    'PoVGkG RVJiJ RVMiM RVQlQ RWZiZ RWFWQ RWTW[ RaEaQ RWTiTi[ RWFRK RXCVI',
	    'Po]JlJ R^NkN RRTYT R^TkT R^ZlZ RRFRV RRFYFYT R^H^[ ReHeY R_D[M RfDdJ',
	    'Po[JeJ RRUWU RbYeY Rh[l[ RRERW RREWEWU R]O]Y RaLa[ R[DeDeJ R]OeOeX RgGgU RlDl[ R[D[PYX',
	    'Po]IkI R]MkM RRTXT R\\UlU R\\ZlZ RRFRW RRFXFXT R\\Q\\[ R]E]M R]EkEkM R\\QlQl[',
	    'Po\\KbK ReKjK R\\QjQ RRSYS Re[l[ RRFRV RRFYFYS R\\E\\P ReQe[ RdEjEjP RlWl[ R_V\\ZX[ R`Q^X RbD\\E',
	    'PoZEkE R[NjN R[SlS RRSXS RRFRV RRFXFXS R[IjIjN RcReWhYl[ R[I[TXZ RcU`Y[[ R[NZX RcPbW',
	    'Po[GkG R\\LjL RZQlQ RRTXT Re[k[ RRFRW RRFXFXT ReQeZ RlWk[ R^D_G R`TZ[ RgDdG RbGbP^W RbIaT',
	    'PoZHlH R\\RiR RRTYT R\\ViV Rc[k[ RRFRV RRFYFYT R]M]X RcKc[ R[NjMjV RfIlN RkXk[ RaHZN RbC_K',
	    'Po[GkG RZMlM RRTYT R^UiU R^ZiZ RRFRV RRFYFYT R^P^[ R`C`M RgCgM R^PiPi[',
	    'Po[FlF R_LkL R\\LhL R]RjR RRTXT R]VjV Re[i[ RRFRW RRFXFXT R]O][ R]OjOjZ RcCdF RgHkM RbF\\L',
	    'PoRSXS R_[g[ RRFRV RRFXFXS R_T_[ RgXg[ RhUlY RcChH R_RdT R^IgIlJ R[MhMfR R[UYZ RbC]IXJ',
	    'PoYHbH RRSXS RRFRU RRFXFXS R^C^H RhWk[ RfNiQ RgDlM RcViX RfDbM R`[][ R\\NaNaW`[ R\\H\\OZX',
	    'Po\\NjN RRTXT RZUlU RRFRV RRFXFXT R\\G\\T RaCaT ReCeT R\\GjGjT RgVl[ R`V[[',
	    'PoZGlG RRSXS RRFRV RRFXFXS RbCb[ R]L`P RbPjX RgMkP RbPZY RhJeP R^JZQ',
	    'Po[GkG RZMlM RZQlQ RRSXS RRFRV RRFXFXS RcCcG R^H`L RgHfL RaM]VjZ RhQfUcXZ[',
	    'PoYJaJ RcLlL RRTXT RRFRU RRFXFXT R^C^[ RiLi[ R^M`P R]JYU R]HZS RkDcFcRbY',
	    'PoZJkJ RZPlP RRTWT RZUkU R[[j[ RRFRW RRFWFWT R]J]U R[EcEc[ RhJhU RiD`E',
	    'PoeRiR RRTXT R_[g[ RRFRW RRFXFXT R_T_[ RbSeV RgXg[ R`K[P RjTlY R^C[J RbF_M RgFcP R\\TZZ R]GkGiQ',
	    'Po\\ElE R^JkJ R]OlO RRSXS R]TkT R[ZlZ RRFRU RRFXFXS RdGdO RdQdZ R\\E[QZX',
	    'Po_IiI R_MiM RRRXR R`WhW Rg[l[ RRFRU RRFXFXR R`R`X RdGdP R`RhRhW R\\ElEl[ R\\E\\Q[X',
	    'PoRTXT Rf[k[ R[[_[ RRGRV RRGXGXT R_P_[ RgCg[ R[MdL_Q R_G_J RbFdJ R[EZK RcD[E RlUl[ RdSZT',
	    'PocGjG RZKlK R]RiR RRTWT R]ViV Re[i[ RRFRV RRFWFXT R]N][ R^E^K RcCcK R]NiNi[',
	    'PoZElE RRSXS R^[b[ RRFRU RRFXFXS RdQdY RgRlW RkJhMeMiU RcE_IYK R_JbM_OYQ RaKdR_UXX RcEgS RcNdV',
	    'PocFlF R\\NkN R\\SkS RRSYS RYWlW RRFRV RRFYFYS R\\J\\S RcCcJ RcSc[ R\\JkJkS',
	    'PoRFlF RbPhP RZXdX Re[j[ RTKT[ RZRZZ R_C_G RaKbP RZReReX RTKkKk[ ReFcK RXF[KZOVQ',
	    'PoRFlF RWNgN RYXeX Re[j[ RSKS[ RYRYY R_C_F R_K_R RYReReX RSKkKk[ ReFcK RXFZK',
	    'Po[GjG RZKlK R[PkP RRTXT RZUlU RRFRW RRFXFXT RbCbK RbPb[ R^L_O RgLeP',
	    'PoYJaJ RcJkJ RRTWT RRERW RREWEWT R^C^[ RgCg[ R]KbP R\\MXW RgKbW RgHkV R^HZU RgHcU',
	    'PoRSWS RbSgS Re[j[ RRFRV RRFWFWS RZDZ[ RbJbU RbJgJgS RaEkEj[ R]L`R RZE_D]L R_U\\U',
	    'Po[FaF RZQkQ RRTXT RRFRW RRFXFXT RbNb[ RbHcL R]VYX RbQ[X R\\I]M RbQhWlX RiGgL RjD_F',
	    'Po\\SjS RRTXT RY[l[ RRGRV RRGXGXT RcSc[ R[GlGkJ R_N[O RfGfOlMlO RcDcG R[G[K R`G_M',
	    'PoZFlF R]NiN RRSXS RZVlV R^[c[ RRFRU RRFXFXS R]I]N RcCcG RcTc[ R]IiIiN R\\QiQcT',
	    'Po^DkD RTH\\H R]OlO RRP\\P R_ZjZ RXHX[ R_R_[ R^IiIiO R_RjRj[ RUDSM RcEaO',
	    'PoRI\\I RaIlI RRM\\M RaMlM RYWdW Rf[l[ RRER[ RYQYY RRE\\E\\M RaEaM RYQdQdW RaElEl[',
	    'Po[GkG RRTYT RZTlT RRFRV RRFYFYT RcQc[ R^LaO RgLkP R_I\\O RhIeO RcCcG',
	    'Po[FkF RZMlM R\\QjQ RRSYS RcUkU Ra[l[ RRFRV RRFYFYS RcCc[ R\\JjJjQ R]SZZ R\\V`Zg[',
	    'Po\\JlJ R_RkR RRTXT R^ZlZ RRFRW RRFXFXT R^U^Z R_M_R ReLeZ RkMkR R\\DlDlJ RlUl[ R\\D\\QZZ',
	    'PoSD_D RTM^M RWZgZ RWRW[ RTH^H^M RWRgRg[ RbGcKhOlQ RcC_K RaGjGhKdN_Q RTHTMSR',
	    'PoTM^M RWZhZ RWSW[ RUG^G^L RWShSh[ RlNkQ RYDYF RgPjQ RfDjF RjJfN_Q RdDeN RUGULTQ RfH`I RkHdH',
	    'Po^LhL R[PiP RRTXT R\\WhW Re[j[ RRFRW RRFXFXT R\\R\\W RbLbW RhRhW R^CZK R_I\\N R\\HlGlUk[',
	    'PoRSYS RRFRV RRFYFYS RkFgI RkQgU RbIkN RdUgYl[ R^E\\J R]Q[U RbI_M[N RcRaV_XX[ RcCbK RcOdV',
	    'PoeMjM RRSYS R]UcU RfUlU R]ZlZ RRFRV RRFYFYS R]P][ RePlPl[ R_I[M RaC]K R`GlGjM RcO]P',
	    'PoRSXS RgUjU Rd[l[ RRFRU RRFXFXS ReLeZ RdLjLjT R\\Q_UZ[ RlWl[ R]KZS RaP]X RcCcG R]MbM_V R[G[K R[GkGkK',
	    'PoYH_H RbOkO RTY]Y RaYjY RTFTO RTST[ RYCYO RTS]S][ RaSa[ RbCbO RaSjSj[ RlLkO RkFaI R^NRP',
	    'PoVIhI RRLlL RZWdW RUZiZ RUOU[ RVDVI RZRZV R_I_L RZRdRdV RVDhDhI RUOiOi[',
	    'Po[FlF RZLlL R\\PjP RRSXS RZSlS RRFRU RRFXFXS RcCcP R\\IjIjP RaP_S]Wk[ RhUdYZ[',
	    'PoRSXS RRFRU RRFXFXS ReUj[ RhJkM RiHdN R`IZN RdRfX R[HaK RdHjK RdEkEhK RZEaE^K RZQaQ^W RZU`V[ZX[ RcQkQiUfYa[',
	    'PoYLcL RRSXS RdSkS RRFRV RRFXFXS R_E_[ RdGdS RdGkGkS R[SVX R^LaSdWkY R^KZU R^JaS RbDYF',
	    'PoZElE R[K`K RRTXT ReTjT RY[l[ RRFRV RRFXFXT R`E`K R[K[T`T`[ ReTe[ ReEeKjKjT',
	    'PoUM^M RVY_Y RUGVSV[ RUG^G^M RVR_R_Z RgVl[ RgUa[ RXCZG RdOiY RiNeX RcJgV RcIkIgV RdCaO RTNSZ',
	    'PoTI_I RaIlI RVZhZ RVRV[ R[I[P RhIhP RVRhRh[ RTETKSP RaEaJ`O R^DTE RjDaE',
	    'PoZH`H ReHlH ReNlN RRSYS ReTlT RRFRU RRFYFYS ReCe[ R_VZ[ R`CaSYU R[N`N_W',
	    'PoZElE RRUXU Rg[k[ RRHRW RRHXHXU R[J[[ RcEcZ R[JkJk[ R]N_NaV ReNgNiU RgNeU R_N]U RgNeN',
	    'Po[GcG RZLdL R_QcQ RRTXT RhWlW RRFRV RRFXFXT R[P[X R_G_X ReFe[ ReFlFlW R\\CZJ RcWXY',
	    'Po\\KjK R\\PjP RRSYS RZUlU RRFRV RRFYFYS R\\F\\O RfPf[ R\\FjFjO RcCcFcO`R\\S',
	    'Po^KhK R[OkO RRUWU R\\ZiZ RRFRW RRFWFWT R\\S\\[ RcKcS R\\SiSi[ RcDfHlJ RbC]IYJ',
	    'PobIlI RYI`I RRTXT R`ZlZ RY[][ RRFRV RRFXFXT R]C][ RfCfI R`PYQ RjKgX RcLdX',
	    'Po[FkF R\\IjI RZLlL RRTWT RRFRW RRFWFWT R\\O\\W RcCcL R\\OjOjW RdXk[ RaYY[ RcRbX',
	    'Po[EkE R\\IkI RZLlL R]SjS RRTXT R]VjV Rf[j[ RRFRW RRFXFXT R]O][ RcCcL R]OjOj[',
	    'PoZGcG RZMdM RZSdS RRSWS RRFRV RRFWFWS R_O_Y R_C_M RgCg[ RgKlP RdXYY',
	    'PoZIaI RdQlQ RRTWT RdZlZ RZ[^[ RRFRW RRFWFWT R^C^[ RdHd[ RdHlHl[ RhCfH RaOZQ',
	    'Po\\GhG R[LlL RRTXT R_UjU R_ZjZ RRFRW RRFXFXT R`P`[ RcCcL R]QjQj[ RkEbNZR',
	    'PoTFjF RRLlL RZVdV RVZhZ RVOV[ RZSZV R_C_L RZSdSdV RVOhOh[ RWHZJ RfGeJcL',
	    'PoYGaG RcGkG RaLlL RYObO RRTWT RRERW RREWEWT R^K^[ RdUi[ RdQkQgX R]DYO RfCdQ RbTYU',
	    'PocGjG RZLlL RRTWT R\\YjY RRFRW RRFWFWT R\\N\\Y R^E^L RcCcL RjNj[ RcQgV RcO`V',
	    'Po_IiI R_NhN RRTXT R^UiU R\\YlY RRFRW RRFXFXT R\\D\\[ RdIdU R\\DlDl[ RfQhS',
	    'Po]KlK R_PkP RRTYT RRFRV RRFYFYT R]E]K RgEgK R]ElElK RbSfU RaN\\S RbEbJ_P RjQeW\\[ RbGbM',
	    'Po\\IkI RZMlM RRTXT R_ZjZ RRFRW RRFXFXT R\\E\\I R_Q_[ RcCcI R^SjSj[ RkEkI R`PZV RaM_S',
	    'Po[PaP RRVWV R[XaX Rf[j[ RRFRY RRFWFWU R[H[Z R[HaHaX RfOhT R^C\\H RfCcL ReHlHlSj[',
	    'Po\\IjI R[NlN RRTXT RcWgW R_[i[ RRFRW RRFXFXT R_R_[ R_RgRgW R]E_G RfNlS RiDfG RjXj[ R`N[S RcG^Q RcC`N',
	    'Po\\MkM R\\RkR RRTXT RZVlV RRFRV RRFXFXT R\\I\\R RcIc[ R\\IkIkR R^D`F RgFcJ RhCfG',
	    'PoRUWU RRFRX RRFWFWT ReCeN RdUjZ RZK\\N RZD\\G R`GkGjK RhTbZ RbOfW R`NjNfW R[SY[ R`G`Q^Y',
	    'Po\\LkL RcRjR RRTWT Rc[l[ RRFRW RRFWFXT R[G[J RdLdZ R[GkGkJ RbCcF R]Q[Z R]S^WaZh[',
	    'Po[KbK R[PbP RRTWT RRFRW RRFWFWT R[G[Z R[GbGbP RfEf[ R]C_E R`SbY RaVZY RfElEiMlSlWhX',
	    'PoZJlJ R[NjN RRTWT RRERW RREWEWT RbCb[ RjPj[ R[FjFjM RfRgX R_R]X R[QZZ',
	    'Po\\JkJ R\\OlO RRTXT R^ZkZ RRFRV RRFXFXT R^S^[ RdJdS R\\EkEkJ R^SkSk[ R\\E\\QZZ',
	    'Po]HjH R]LjL RZOlO RRSXS Re[i[ RRFRU RRFXFXS R]D]L R]DjDjL R^PYU RaSZZ ReV`[ RfRdX R]SkRi[',
	    'PoSEkE RdRhR RUZiZ RRJRM RUUU[ RVMVR R_C_F R_J_S RVMhMhR RUUiUi[ RYFZJ RfFdJ RRJlJlM',
	    'Po[GlG RRSXS RfYjY RRFRV RRFXFXS R[L[O R\\Q\\Y RcCcG RcNc[ R\\QjQjX R[LlLlO R^G_K RhGfK',
	    'Po_IgI R]QiQ RRSYS RYVlV RRFRU RRFYFYS R]L]Q RfSf[ R]LiLiQ R_W[[ RcCfGlI RbC^GZI R_S^Y',
	    'PoYKaK RRTWT RRERW RREWEWT R^D^[ R]MaQ RhSk[ RfRb[ R^LYW RkIjP R^I[T RgCgPdY RdIcO RaDYE RgJiW RgKeW',
	    'Po[FlF R^JhJ R^MhM RZQlQ RRTXT RfVkV R\\ZkZ RRGRV RRGXGXT R\\Q\\[ R^C^Q RfQfV RhChQ RaQ`V',
	    'PoRSXS R^ZiZ RRFRV RRFXFXS R[F[J R^S^[ RcCcF R[TiSj[ R[FkFkJ RaI[N R_KcPkT R_KhKcPZT',
	    'Po]IkI R]MkM RRSXS RcWiW R^WeW Rh[l[ RRFRV RRFXFXS R\\Q\\[ R]D]M RdDdV R]DkDkM R\\QlQl[ RgSiX',
	    'Po\\IjI R\\MjM RZQlQ RRSXS RRFRU RRFXFYS R\\D\\M R]Q]Z RcDcM R\\DjDjM RcQeVhYl[ RkTeV RdYZ[',
	    'Po[FlF R^QiQ RRSXS R^UiU Rh[k[ RRFRV RRFXFXS R[K[[ RcCcK RcQcZ R[KlKl[ R_M`O RgLfO',
	    'PoTFjF RUJiJ RSMkM RRRlR RVZhZ RVUV[ R_F_R RVUhUh[ RWOYQ RXD[F RfOeQ ReDcF',
	    'Po\\IjI R\\NjN R[RlR RRTXT RY[l[ RRFRW RRFXFXT R\\E\\N R\\EjEjN RbOdR R_T`Z RhTf[',
	    'PoRH^H R_HlH RSN^N R`NkN RTZ]Z RaZkZ RTRT[ RXCXN RTR]R][ RaRa[ RfCfN RaRkRk[',
	    'PoZGdG R[QdQ RRTWT Rh[l[ RRFRW RRFWFWT R[K[Q R_C_[ R[KdKdQ RhFhT RlDl[ RaSdV R^RcT R^TXY R_P\\V',
	    'Po\\HbH RRSXS R\\VjV R\\ZjZ RRFRV RRFXFXS R\\C\\N R\\Q\\[ RaOaQ R\\QjQj[ RkFdI ReCeNlMlN RbLZN',
	    'Po^IlI R`NkN R^SlS RRTXT RRERV RREXEXT R\\I\\[ RaEiEhI ReNeTkZ R^CYN RaK^Q ReTaY][',
	    'Po[HlH R]KdK RRTXT R^UcU RRFRV RRFXFXT R^O^W R^OcOcU RhCkF RgTa[ RjMfW RlWk[i[ RfDfMhY R[H[TY[',
	    'PoZGlG RbLhL R\\OkO RZSlS RRTWT RRERW RREWEWT R\\D\\O RbDbL RcOc[ RhDhL RcRhXlZ RcR^XXZ',
	    'PoUK^K RcKlK RUEUK RUE^E^K RaMa[ RcEcK RkMk[ RcElElK RWVR[ RSPUT R[P]T RePgT RXLXRWW',
	    'Po_HbH RZRlR RRTWT RRGRW RRGWGWT R[F[O R_D_N RcOc[ ReDeNkMkN RcRhWlY RkFeI RbR\\XYY RbMYN',
	    'PocHjH RRSYS R`WhW Rg[l[ RRFRU RRFYFYS R[N[[ R^D^N R`R`X RcHcN R`RhRhW R^DjDjN R[NlNl[',
	    'Po[GlG RZNlN RRTXT R_ZjZ RRFRV RRFXFXT R_S_[ R`C`J RgCgJ R^SjSj[ R^RZW RbK]T',
	    'PoYHcH R\\QbQ RRTWT R\\UbU R_[b[ RRFRU RRFWFWS R\\J\\[ R[MbMb[ ReEe[ RjNlT RlWhW ReElEiN R]DZO',
	    'Po[FkF R[MlM RRSYS R]UjU R]ZjZ RRFRV RRFYFYS R]Q][ RcDcG R]QjQj[ R_H`L RhHfL',
	    'PofHlH RcOlO RRTXT R]ViV R]ZiZ RRGRV RRGXGXT R]R][ RfDfN R]RiRi[ R]FYJ R]GcFaK]OZP R^C\\H^KbNiO',
	    'PoZElE RaNfN RZRlR RRSXS RRFRV RRFXFXS R[I[O RaEaN RfEfN R[IkIkO R`P]Vl[ RgTcY[[',
	    'Po\\IkI R\\LlL R]RjR RRSXS R]VkV R\\[l[ RRFRU RRFXFXS RdSdZ R\\DkDkI RgNkR RbL`R R\\D\\PZY',
	    'Po[ElE RaPeP RRTXT RaTeT R\\ZkZ RRFRV RRFXFYT R\\J\\[ RaJaZ ReJeZ R\\JkJk[ RcEbJ',
	    'PoXI`I RbJlJ RRVWV RbZkZ RRGRX RRGWGWU R]C][ RbQb[ RgCgQ RbQkQk[ R]J_P R]GZS',
	    'Po\\IkI R\\PjP RRSXS R\\TjT RZWlW RRFRU RRFXFXS R[D[G R\\L\\S RcGc[ R\\LjLjS R[DkDkG',
	    'Po[JkJ RZMlM RRTWT Rh[k[ RRFRV RRFWFWT R[R[[ R[D[J R`R`[ RcCcJ RfRf[ RkDkJ R[RkRk[ RbM`T',
	    'Po\\JgJ RZMlM RRSXS RRFRU RRFXFXS RdTdY RfTkY R_ObQdV RdNgV RcMZQ RbQYU RjQeR RbUXZ R]C\\J R^EhEfM Rc[^[',
	    'PodOiO RRSXSlT RRFRU RRFXFXS R\\H\\T ReJeO R[JiJiT R`M]P R^FYK RcUfYk[ R^D^FeFcI RbR`W^YY[ R`J_O',
	    'Po[KkK RZOlO RRUXU RRFRW RRFXFXT R]VY[ R\\F]I R[EcEcI R_RdXk[ R_Q\\Y RiFgK R_SiSeX][ R`L]W RjCbD',
	    'PoSFkF RUIiI RWOgO RRSlS RVZhZ RVVV[ R_C_I RVVhVh[ RYOZS RdOcS RWLWP RWLgLgP',
	    'Po]IkI R]MkM RRSYS R]YgY Rf[k[ RRFRV RRFYFYS R]E]M R]Q]Y R]EkEkM R_NYU ReS]U R^QlPlUk[',
	    'Po[JcJ R[OcO R[SbS RRTXT R[WcW RhWlW RRFRV RRFXFXT R[F[[ R[FcFcO ReFe[ ReFlFlW R^C]F',
	    'Po\\HlH R\\MlM R]SkS RRTXT R]WkW Rg[k[ RRFRW RRFXFXT R\\D\\L R]O][ RdDdL R]OkOk[ R\\DlDlL',
	    'Po[EaE RZIlI R\\PjP R\\TjT RRUXU R[WkW RY[l[ RRFRW RRFXFXU R\\L\\T RcEc[ R\\LjLjT RiC`E',
	    'PoZJlJ R]PiP R[TkT RRTXT R\\XlX RRFRW RRFXFXT R\\U\\X RcPc[ R\\FiFiJ R]LiLiQ R]L]Q RaC`J',
	    'Po[JlJ RRTXT R\\TaT ReTjT R\\ZjZ RRFRW RRFXFXT R\\O\\[ R\\EcEcY ReNjNj[ RaN\\O RiD`E',
	    'Po^PeP RSSZS ReUiU RSESS RSEZEZS R\\W\\[ R^J^R R^JeJeP RhWl[ RUWTZ RbWc[ R`C]I R_GkGkNiT',
	    'Po\\JjJ R\\NjN R[RkR RRTYT R\\VjV RZ[l[ RRFRW RRFYFYT R\\F\\N RcRc[ R\\FjFjN RbDbF',
	    'Po[FlF R]KkK R`PhP RRTXT RRFRV RRFXFXT R`H`O RhHhO RcCdF R^ScXfYl[ R]SiSeWaY[[ R[F[RZY',
	    'PoXJfJ RRR\\R RbRlR RTZjZ RRMRR RTUTZ RXDXJ RRM\\M\\R R_P_Z RbMbR RXDfDfJ RjUj[ RbMlMlR',
	    'Po^KhK R]SiS RRTXT R]WiW RZ[l[ RRGRV RRGXGXT R[G[J R]N]W RcCcG R]NiNiW R[GkGkJ',
	    'PoZFlF R]MiM RRSXS Rf[l[ RRFRU RRFXFXS RZPZS RcCcF RfSf[ R]IiIiM RZPlPlS RlXl[ R`T^X\\YX[ R]I]N',
	    'Po]KgK RRSXS R^[a[ RRFRU RRFXFXS RZOZ[ RZOaOa[ R\\Q_S R\\U_W RcTf[ RhTiWk[ R_EcDhJlK RfNcU RjNhU RcE\\JXL',
	    'PoSFkF RUN\\N RbNiN RRQlQ RUIUN RXQXZ RUI\\I\\N RbIbN RbIiIiN RiSbV RaYT[ R_C_RcXgZl[',
	    'PoZFbF RZJaJ RRSXS RYTlT RRFRU RRFXFXS R^C^N RbTdWfYk[ RbNYO RbQbT_YY[ RfEeMcO RdElEkPgP',
	    'PoTEaE RSHkH RYOeO RZYdY Re[i[ RTQT[ RYJYO RZTZZ RZTdTdY RXKfKeO RTQjQj[ RcHfKlM R\\FXKRN RgDaD',
	    'Po[FlF R\\MlM RRSXS RRFRV RRFXFXS RcCcF R^HhJ RgGcJ]K RfO^Q RiRdU^V RkVeY][ R\\M\\SZY',
	    'PoUI]I RaIjI RUOiO RUSiS RRWlW RUDUI RUKUS RUD]D]I R_K_[ RaDaI RUKiKiS RaDjDjI',
	    'PoZGkG R^MgM RZTkT RRUXU RRHRW RRHXHXT RbJbQ RgQg[ R]WY[ RaG[M RdGhLlM RbC_J R^Q]X',
	    'Po_HgH R]OiO RRRYR R]SiS RRFRU RRFYFYR R]L]Z R]LiLiS RcChIlJ RcTfYl[ RkTeW RcC\\JZJ RdY[[',
	    'Po[ElE R_MjM R_QjQ RRSXS R^UkU R]ZlZ RRFRV RRFXFXS R_I_Q RdIdZ R_IjIjQ R[E[RZZ',
	    'PoYPbP RdPjP RRTXT Rf[j[ RRFRV RRFXFXT R^O^[ RZH]L R`RbW R`LbQ R^C[I R`G]M R[RZY ReCcL RdIlIlUk[',
	    'PoZGkG R]TiT RRUXU R]WiW RZ[l[ RRFRX RRFXFXU R]P]W RcCcN R]PiPiW RcGhLlN RbG]LZN',
	    'Po[GkG R\\KdK R\\OdO RRTXT RRFRW RRFXFXT RlXl[ RiDjE RjOeZ RdQaW R]UbV^Y[Z RjKgW R`K^T RfCfMhY R[GZSY[',
	    'PoZGlG R\\SjS RRUXU R\\ZjZ RRFRW RRFXFXT R\\M\\[ R_D_K RcMcY RgDgK R\\MjMj[',
	    'PoWQhQ RUZjZ RUTU[ RWKWP RWKhKhP RUTjTj[ RTDWG R]C_G RiDeH R^Q]T RRHRL RRHlHlL',
	    'Po[FkF RZKlK RRTWT RRFRW RRFWFWT R]O]W R^H^M RcCcH RhHhM R]OjOjW ReWl[ RaYY[ RcRbW',
	    'PoRFlF RULiL RUOiO RSY\\Y Ra[k[ RSSS[ RUIUQ RSS\\S\\Y R_C_Q RaRaZ RUIiIiQ RlWk[ RcDgD RkTaV',
	    'PoZGkG R]MhM R[RjR RRVWV R[ViV RY[k[ RRFRY RRFWFWV RbSb[ RcJcR RaF[L ReGhKlL RbC_I',
	    'Po`LfL ReQkQ RRTXT RRFRV RRFXFXT R]G][ RdEcH`K RiTlY RiMkR R^CYN RbT_Y RiH`QgQf[b[ RjD`E',
	    'Po]JgJ R[QaQ RRTWT R[UaU Rf[j[ RRERW RREWEWT R[M[[ ReNeW RjMj[ RcDeGlJ RcC_HYJ R[MaM`[a[_[',
	    'Po]NiN R\\RiR RRTXT R_[g[ RRFRW RRFXFXT R_U_[ R]JiJiR RbTdW RjVkZ RgXg[ R_D[I R[VYZ R^FgFeJ',
	    'PoZIlI RZSlS RRTXT RRFRW RRFXFXT RcCcO R\\D^G RhDgG RcHeKlN RcH_LZN R`P_T\\Vj[ RgTcX`YZ[',
	    'PoWMiM RRQlQ RVZiZ RSHSM RVUV[ R_J_Q RVUiUi[ RSHlHlM RUDXF RXLTO R^C_G RhCfH',
	    'PoXIfI RSP]P RaPkP RRTlT RSKSP RXDXI RSK]K]P R_Q_[ RaKaP RXDfDfI RaKkKkP RaTcVkZ R\\VRZ',
	    'PoRF^F RSJ]J RiLlL RTP\\P RSY]Y RRMRP RSTSZ RXCXJ RST]T]Z RRM^M^P RcEiEiL RcQeVhYlZ RaPjPhUdY_[ RcFbM',
	    'PodHkH RcOiO RdRkR RRSXS Rg[l[ RRFRU RRFXFXS ReFeQ RhTh[ R_KkKkN RlXl[ RaZ_[ RcTaZ R[E[QZX R_K_S]Y RjC]E RfN`O',
	    'Po[FaF R]OkO RZSlS RRTXT R\\ZjZ RRFRW RRFXFXT R\\U\\Z RcNcZ RbGcJ RjUj[ R^M[P R\\G^K RjFhK RjDaE',
	    'Po[FlF R^MiM RRTXT R_XgX Rh[k[ RRFRV RRFXFXT R[P[[ R^I^M R_S_Y RcCcF R_SgSgX R^IiIiM R[PkPk[',
	    'PoZElE R\\KiK R\\NiN R\\QiQ RRSYS RRFRV RRFYFYS R\\H\\Q RbEaH R\\HiHiQ R_TXW R`Q]U`WdYl[ R]TiTfWbY[[ RcYX[',
	    'Po\\IiI R\\MiM R\\PiP RZTlT RRUXU RRFRV RRFXFXT R\\F\\P R\\FiFiP RfQhS RbCaF RbTfYl[ RcRaW_YZ[ RcRcV',
	    'Po_IfI R\\OiO R\\RiR RRSXS R]ZiZ RRFRV RRFXFXS R\\L]U][ R\\LiLiR R]UiUi[ RcChIlJ RbC]IYJ R\\O[X',
	    'PoTFjF RRNlN RZWdW RUZiZ RUQU[ RZSZW R_C_N RZSdSdW RUQiQi[ RXI[L RfHaL RYHTL RdJiL',
	    'Po]OiO RRUXU R\\VjV R\\[k[ RRHRW RRHXHXU R\\Q\\[ R]J]N RcQcV R]JiJiN R\\QjQjW R[F^I RaFdI RgFiI RiCgF RlXk[ R^D[F RdCaF',
	    'Po[FkF R\\IjI RZLlL RRSXS RZUkU RRFRV RRFXFXS RcTc[ RhWkZ RhRlU RcCcL^P R^VZZ RfRjS R[ObR RfNbR^T',
	    'Po\\EjE R[JlJ R\\PjP R\\SjS RRUYU R\\WjW RRHRW RRHYHYT R\\L\\W RcEcJ R\\LjLjW ReYl[ RaYZ[',
	    'PoeHjH RRSXS R\\TjT R\\WjW Rf[j[ RRFRV RRFXFXS R\\Q\\[ R\\QjQj[ R[EkEiH RhKdN R^I\\N RdKlKkNhN RcD`G[I RZKbKaN^N',
	    'Po^JgJ RaNgN RRSXS RY[l[ RRFRU RRFXFXS R\\T\\[ R`T`[ ReTe[ R\\TiTi[ RgOlT RaEfEkJ R^OYT R^FZJ',
	    'PoZIdI RfKlK RfRlR R[SbS RRTWT Rh[l[ RRFRV RRFWFWT R[K[S RbKbT RfDlDl[ R[C]G R[ZYZ RbC_I_Q]W RfDfQeY',
	    'Po]EkE R]IkI R]MkM RRSYS Rf[j[ RRFRV RRFYFYS R`U`Y RdEdR RhTiW RcTeX R\\TZZ R]E]RlRk[',
	    'PoZGlG R[RjR RRSWS R^[b[ RRFRV RRFWFWS R_D_J RbOb[ RfDfJ RfUjY R^UZY RcJfMlO RcI^NYP',
	    'Po]JkJ R]NjN R[QlQ RRTXT R]ZjZ RRFRW RRFXFXT R\\G[J R]T][ RcHcT R]TjTj[ R\\FlFlI RbCeF',
	    'Po[GjG RYKlK R[QjQ RRTXT RX[l[ RRFRV RRFXFXT R[T[[ R`T`[ RbCbK ReTe[ R[TjTj[ RfMkR R`K^Q',
	    'PoZFlF RRSXS R\\ZiZ RRFRU RRFXFXS R\\S\\[ R_C_I RfCfI R\\SiSi[ RcIhN R^PgOlP RbI]OXQ',
	    'PoRSWS RZTkT RRFRU RRFWFWS RbRb[ RdNkQ R[MaP RbSgXlZ RZLaL]PYQ RcLjLgOcQ RbS^XXZ R]FhI R\\EhEbH[J',
	    'Po]IkI R_NjN R_QjQ RRTXT R_TjT R[WlW RRFRV RRFXFXT R[L[W R]C]H R_K_T R_KjKjT RfXl[ R_XY[ RcE\\F RiD_F',
	    'Po\\EkE R_HjH R\\KlK RRTXT R^TlT Rd[h[ RRFRV RRFXFXT R`K`Q RhQh[ R`VcX RdLhPlQ RkLfN RdP_P R\\E\\PZX',
	    'Po\\J`J RfJkJ R\\NkN RRTXT RRFRV RRFXFXT R]F]N RcCcR RfFkFkN R\\R`WeYl[ RaE\\F R[SjSgWbYZ[',
	    'PocGiG R^RiR RRSXS R^ViV Re[i[ RRFRU RRFXFXS R[K[O R^D^K R^O^[ R^OiOi[ R^DiDiK R[KlKlO RcHcL',
	    'Po[RkR RRSXS RZUlU RRFRU RRFXFXS RfOjP RhFlJ RfK_Q RaJ^M RbFcI R\\MbO RbTcWfYk[ R]E[K RcSaW_YY[ RiC]E',
	    'Po\\FlF R\\PlP RRTXT Rd[h[ RRFRW RRFXFXT R[K[O RcCdF RcMdP R\\KlKlO R_G`J RhGgK R`V^ZZ[ RbQ`X RaUiUh[',
	    'Po]KiK R]OlO RRSYS Re[j[ RRFRV RRFYFYS R_U_Y R]FiFiK RfUhW RbUdX RbCaF R[VYZ R]F]SkSj[',
	    'PoZHlH RYOlO R[SiS RRSXS RRFRU RRFXFXS R`E`[ ReEe[ R[LiLiS RdSjY R_SYY RiDbEZF',
	    'Po[FfF RYIlI R_PkP RRSXS R\\VjV R\\ZjZ RRFRU RRFXFXS R\\S\\[ R_L_P RaCaI R\\SjSj[ RjEhG_KYM RiL^N',
	    'Po[ElE R]LjL RfSkS RRSXS R_VhV Rh[k[ RRFRV RRFXFXS R[O[[ R]H]L RcVcZ R]HjHjL R[OkOl[ RaO`R]S ReOeS',
	    'PoZFlF RRSXS RZZaZ RRFRU RRFXFXS R_C_J RZO^T RcOgT R_Va[ RfCgJeP RjNeY R^J[P R`O[Z R^ZXZ RiVkZdZ',
	    'Po[HlH R\\LjL RZPlP RRTYT R_TkT R\\[l[ RRFRV RRFYFYT RcHcP RdTd[ R^C_G R\\VW[ RhCgG R^P[Y',
	    'PoaZjZ RU[Y[ RYPY[ RaQa[ R_RjRj[ RcP_R RhJaQ RTM^L[O RXGXK R\\F^J RbGdLhPlQ RcFjFgM RUERK RcC`K R]DUE RYSRT R[SUT',
	    'PoaLiL R\\MeM RRTWT R][a[ Rg[j[ RRFRW RRFWFWT RZPaPa[ RcPkPk[ R[R^T RdRgS ReIiM R`D[H RaG\\M ReDhGlH RaUYX RjUcX',
	    'Po\\NiN RRTXT RX[l[ RRFRV RRFXFXT R[R[[ R\\E\\N R`R`[ ReRe[ R\\EiEiN R[RjRj[ RbJeK RbI_L RcFaK',
	    'PoaIiI RSJ]J RRS^S RbUhU Rg[l[ RRMR[ RSDSJ RVMV[ RZMZ[ RSD]D]J RbMbW RbMhMhU R`ElEl[ RRM^M^[[[',
	    'Po[IjI RZMlM RRSXS R[UjU R[ZkZ RRFRV RRFXFXS R[P[U RcMcZ R[PjPkU RhWl[ RbJ]L R[D\\I RjDjI RcDbJ',
	    'Po[JiJ R[MiM RYQkQ RRTWT R\\WiW RRFRV RRFWFWT R[F[M RbSb[ R[FiFiM RbCbF ReQgTlU RaO]TXV',
	    'Po[FkF R]PdP R\\TkT RRTWT R`[c[ RRFRW RRFWFXT RZJZN RcCcJ RcTc[ RZJlJlN RgVkZ RhQkT R^V[Z RbK^P RgN[S',
	    'PoaLjL RYL]L RVWgW RVSV[ R\\S\\[ RaSa[ RaEaK RaEjEjK RWJSM R_MgP RZQcOlQ R`LXPRQ RVSgSg[d[ RXDWK RSF]F]L',
	    'Po`HlH RRTWT R_XlX Rd[i[ RRFRW RRFWFWT RaKkKkV RcSfV RdLgO RYE]F RYL]M RaC^L R[SX[ R]QkQi[ RaK_W',
	    'Po]NiN R]RiR RRSYS R]ViV RRFRV RRFYFYS R]J]V RcCcJ R]JiJiV RgElI R_E[I RfXkZ R`XZ[',
	    'Po[FjF R\\JiJ RYMlM RRTXT R[TjT RRFRW RRFXFXT RcQc[ RfMkR R`L[R RaTiX RcF_O RbT^XZZ RcD`M ReP^Q',
	    'PoZElE R]GhG R]JhJ R\\NkN RYNiN RRTWT RRFRW RRFWFWT R]E]N RhEhO RfUk[ R]VaY RZT]U R_UY[ RjTdZ RdQgX RcQkQhX RYQaQ^W',
	    'PoYFlF RRTXT R`UfU Rf[j[ RRFRW RRFXFXT R\\L\\[ R_C_H R`O`W R`OfOfU RfCfH R_KkKj[ R]IYQ',
	    'PoSEkE RWJgJ RWLgL RWNgN RRQlQ RTY\\Y Ra[k[ RTST[ RWGWQ RTS\\S\\Y R^C^G RaRaZ RWGgGgQ RkXk[ RjSaV',
	    'Po`ElE RRUVU RYV^V RaZlZ RRFRW RRFVFWU RYFYX RYF^F^V RaJa[ RdEdQ RaJlJl[ RhEhRlR RYM^NYN',
	    'Po^IgI R\\QjQ RRTXT R^[h[ RRFRW RRFXFXT R\\E\\Q R_T_[ R\\EjEjQ RcSdW RcKgO RhXh[ RiTlY R[TYZ RaN^O RcGaM',
	    'PobKjK RRSWS RcViV R_YlY RRFRV RRFWFWS R\\G\\[ R_E_[ RcPcV RfGfO RcPiPiV R_ElEl[ R]CYM',
	    'Po^HgH R\\NiN RRTXT R[TkT RYWlW R^[b[ RRFRV RRFXFXT R\\K\\N R\\QcQc[ R\\KiKiN RcCeFlI RbC_FYI RhPaQ',
	    'Po_NgN RRSWS RRFRV RRFWFWS R`OaQ RaVcY ReDjF RbLZO ReS\\W R`DZG R^GdJlL RgGaJYL RfO`RZS RcTjTgWcY[[',
	    'Po\\EeE R[OkO R^RiR RRTWT RRFRV RRFWFWT RbFcI RZJZLkJkM R]UXZ R]F]I R^RcXk[ R_O[X RiFgJ RhSdX][ R`L]V RjCcD',
	    'PoZGkG R[LjL RYQlQ RRTXT ReWjW Rc[l[ RRFRW RRFXFXT RdQdZ RiTiW R^D_G R`TY[ RlWk[ RgDeG RgReW RbGaP^W RbJ`T',
	    'Po_EkE R`OkO R`SkS RRTWT RhXkX Ra[l[ RRFRW RRFWFWT R`J`X ReJeX R`JkJkX RZE]I RiFeH RYM\\M\\WX[ R\\Xc[ RbGhI',
	    'Po[HjH RZPlP RRSXS RZSkS RZWlW RRFRU RRFXFXS R^L^S RcDc[ R[D[LiLhS R[DjDjL',
	    'PoZGbG RYKcK RYPcP RRTWT RRFRV RRFWFWT RhWl[ RgUb[ RfQjY RiPeY R`[][ RdKhW RfCcN ReIkIhU R^C]P[Y R\\TaTa[',
	    'PoTE]E RbHlH RRH_H RSK^K RVZhZ RVSV[ RVShSh[ RVNRR RbHdKgOlQ RYCXKUQ RcC`K RiGhKdN_P RVN]N\\QYQ',
	    'Po^FlF R_NkN R_QkQ RRTWT R^UlU R`[l[ RRFRV RRFWFWT R_J_Q ReCeY R_JkJkQ RYJ\\M RZD\\G RYQ\\Q\\WX[ R^Ze[',
	    'Po\\IkI R\\OlO R]RkR RRTXT R]VkV R]ZlZ RRERW RREXEXT R\\D\\I R]M][ RdCdI RdNdZ RkDkI R]NYR RdKeN R^K[Q',
	    'Po\\GlG R^MlM RbShS RRTXT RRFRW RRFXFXT RbIbS RbVbZ RcCdF RhIhS RfVgZ RjUlZ R^V][ R\\G\\R[X',
	    'PoZFlF R\\MjM R\\QjQ RRTXT RZUlU RRFRW RRFXFXT R\\J\\Q R`C`H RgCgH R\\JjJjQ RbTfYl[ RbV_YZ[ RbQaX',
	    'PoXGbG RYQaQ RRSWS RRFRU RRFWFWS RYKYQ R]C][ RYKaKaQ R]RaV R[TWY ReWa[ RhIgSkZ RfScY R]OZV RdIkIjN ReCcN RgMfV',
	    'PocHlH RYHaH RdMkM RbSlS RRTWT RRFRU RRFWFWS R]C]H R[N`M`Y RfWa[ RgMgTkZ RgSdY RdDaK RdJcQ R_[\\[ RgOfW R[H[PYY',
	    'Po^HgH R\\QiQ RRSXS R\\TiT R\\WiW RRFRU RRFXFXS R\\N\\W R\\NiNiW R\\KhKfN RfXl[ RdElH R_XY[ RaEYH',
	    'Po[FlF R^OhO RRTXT R_WgW RRFRW RRFXFXT R\\K\\[ R_S_Y RcKcR R_SgSgW RcCcF R_G`J RhFfK R\\KkKk[g[',
	    'PoZGlG R_LdL R[PdP RRTWT R]Z`Z RRFRW RRFWFWT R`I`Z RhDjF RgVcZ RcSdV RkNfY R\\S\\X RlWj[k[ RkLhV RgCfNhY RZGZSXZ',
	    'PoRMYM ReMlM RRYYY ReYlY RRQR[ RRERM RREYEYM RRQYQY[ R\\G\\T RbDb[ ReQe[ ReEeM ReElElM ReQlQl[ RaR[T',
	    'Po[FlF R[LlL R^OjO RRTXT R^TjT R^WjW Rh[j[ RRFRW RRFXFXT R^Q^[ RdEd[ R^IjIjO R^QjQj[ R[F[QZY',
	    'Po[HhH R[JhJ R[MlM RfSiS RRSXS RZTlT RRFRU RRFXFXS RbQb[ R[EhEhJ R[E[OjOiR RbTfWlZ RbT_XYZ',
	    'PodElE RZJaJ RZOaO RbOlO RRTWT Rh[l[ RRFRV RRFWFWT RZFZO RZFaFaO R[P[XaWaY RdEdO RhLh[ RlWl[ RfV`[ RhPdY R]C\\F R`RZT RiGgT',
	    'PoZFlF R[OjO R[RjR RRSXS RYVlV RRFRV RRFXFXS R[J[O R_C_H RfCfH R[JjJjO RbJbV_YY[ RbVeYk[',
	    'PoZHjH RYN_N RRSYS RRFRT RRFXFXS ReEhFjI R`D\\H RbKgPlR RhJfN R[JYN RbK\\QXR RaR[U ReS[X RiVcY[[ RkMeN',
	    'PoZFlF R[MkM R[QkQ RRUXU R\\WjW R\\ZjZ RRFRW RRFXFXU R[I[Q R\\S\\[ R`C`Q RfCfQ R\\SjSj[ R[IkIkQ',
	    'PoSElE RUIjI RVOiO RSRlR RaZkZ RY[][ R_C_H RaUa[ RVKiKiO RaUkUk[ RZP[R RdPcQ RXTVYR[ RVKVO RSV^V][',
	    'Po\\HkH R[KlK R_OiO R]SkS RRSXS R^VjV R\\ZlZ RRFRU RRFXFXS RcCcH RdLdR RdTdZ RkDkH R\\D\\H R[K[SZZ',
	    'PoYG_G RXKbK RRTWT RZ[][ RRGRV RRGWGWT R]D]K R^R^[ RhWl[ RYOaO]S RgUa[ R_KXR RfQjY RiOeX RaE^M ReLhW RfDcN ReIgU ReIkIhU RbU]UXV',
	    'PoZDlD R\\MkM R\\QjQ RZTlT RRTXT R_[c[ RRFRW RRFXFXT R\\H\\M R`D`M RcTc[ RfDfM R\\HkHkM RhVlZ R^VZZ',
	    'PoYHlH R\\KiK R[QjQ RRSXS RYTlT RRFRV RRFXFXS R[N[Q R\\E\\K RcCcQ R\\EiEiK R[NjNjQ R`Q_T]Wk[ RgUdYZ[',
	    'PoZDlD R\\JjJ R\\MjM RRSXS RRFRV RRFXFXS RcEbH R\\HjHjP R\\H\\PgQiR R`RbWeYi[ RZTiUeX[[ RkYk[h[ RkTgT RfXY[',
	    'PoRIZI R\\LbL ReLkL R\\PbP R\\VcV RSXYX RSPSZ RVDVP RSPYPYX R\\E\\[ R\\EbEbK ReEkEkK RfVk[ RdPhY RdPkPhVeYa[',
	    'PoRRXR RRERU RREXEXR R[DaDaL RdDkDkM RhIeL R^F[I R_I[M RgFdI RcNeOlR RbM`PZS RcR\\U RfT^W RiVdY\\[',
	    'PocEjE RaKhK R^LcL RcNkN RRTXT R]VlV R`[d[ RRGRV RRGXGXS RcCcN R^QdQd[ R_RaT RjRhT R[HlHjK R[H[RZY RiPbP',
	    'PoZFbF RdFlF R[IbI RdIkI RdMlM RRSXS RYVlV R[ZjZ RRFRU RRFXFXS R^C^O RhChO R\\QjQj[ RbLYM',
	    'Po]JjJ R^QbQ RRTXT R[YlY RRFRW RRFXFXT R[D[[ R^L^R R^LbLbQ RjTjW R[DlDl[ RgGjH RgRbW RiMeU RcS]T RfFeLgV',
	    'Po[EkE R`MhM RRSXS R^VcV RfVkV R[ZlZ RRFRU RRFXFXS R[E[[ R^P^V R`H`M R^PcPcV RfPfV R`HhHhM RfPkPkV',
	    'Po[ElE RcHjH RYLlL R]QkQ RRTXT Rf[j[ RRERW RREXEXT R^H^L R`U`Y RcEcL RgUhX RdUdX R[UZZ R]N\\TlTk[',
	    'Po[EkE R\\HjH RZJlJ R]PjP RRSYS R]SjS R]WjW RRFRV RRFYFYS R]M]W RcCcJ R]MjMjW RfXl[ R_XY[',
	    'PoTOeO RURkR RUWiW RUZiZ RUTU[ R_C_G RWIgIhM RUTiTi[ RVDXF RhCfF RTMTQ RRGRJ RRGlGlJ RWIWLhMcN',
	    'PocEkE R^MdM RbPjP RRTXT RZZlZ RRFRV RRFXFXS RbJbP RaRaZ RcCcH RfRfZ R]S^X RjShX R[IlIkL R[H[QYY RgLaM',
	    'PoRSXS RYTlT RRFRU RRFXFXS RfCfJ RZG\\I R[D]F R]LZO RaJeNkP R`FkFkH R`JjJgNaP R`R_U]WkZ RgUdYZ[ R`F_O',
	    'PobHjH RbMjM R`OlO R_SlS RRTXT Rd[h[ RRFRV RRFXFXT R]L][ RbDbL RhQh[ RbDjDjL RaUdW R_DYI R^KYP R_I]N',
	    'PoZGlG R[LbL RdLkL RRSWS RRFRU RRFWFWS R_H_[ RaOaU RbCbG RgHg[ RjOkVlX RZGZRXY R\\P[X RdPcX',
	    'PoZFkF RRTWT RRFRW RRFWFWT RbCbO R]I_K RhIlM RbKfOlQ R`UjZ R]H[M RhHfL RbK^OYQ RaSZV R`ShSdX_ZZ[',
	    'PoZObO RZTbT RRUWU R^[b[ RRGRW RRGWGWU R_J_T RYKbJb[ RiCi[ RdLgO RdFgH R\\FaF^L RlRdT R\\CYJZSYZ',
	    'PoYFcF R[JaJ RZRbR RRTWT RYVcV Rg[k[ RRGRV RRGWGWT RZMZR R[C[J R^J^[ RaCaJ RZMbMbR RgCfSb[ RcIlIlVk[ RgLeW',
	    'PoZGaG RYLdL RRTWT R[TbT R[YbY RRFRW RRFWFWT R\\O\\[ R^C^L RZQbPb[ RfEf[ RcDaJ[PXQ RfFlEiMlRkVhV',
	    'PoZElE R]JiJ R]MiM RRSXS R]UkU R[UhU RRFRV RRFXFXS RZPZR R]H]P RbEaH R]HiHiP RZPlPlR RlYk[ ReQhR R`RbWeYg[k[ RiVeX`ZY[',
	    'Po^RiR RRTXT R\\VkV RZ[l[ RRFRW RRFXFXT R[I[L R^L^R RcCcH RcRc[ R^LiLiR R[HlIlL R]D^G RiDhG',
	    'PoYJaJ RYOaO RYSbS RRTVT Rd[g[ RRERW RREVEVT RYEYO R]E]Y RYEaEbO RhNh[ ReHiL RdEkEiI RcNlNjR RbXXZ',
	    'Po[HlH R[ObO ReOlO RRTXT R[TbT ReTlT R_[b[ RRFRW RRFXFXT R[KbKb[ RdCdH RlEkI R[E\\I ReKlKl[i[ R[K[TZZ ReKeUdZ',
	    'PoZTlT RRTWT RRFRW RRFWFWT R[D[M R[DaDaM RdDdM RdDkDkM R_NaO RhNkQ R\\PYQ RePbP R`Q_T]Wk[ RgUdYZ[ R^F]O RgFfO',
	    'PofGkG R_MkM RfSkS RRTWT Re[l[ RRERW RREWEWT R]L][ RbEbM RfNfZ RfCfM RbWeZ R^DZH R]KYO RaRcX R^H\\N RaP_Z',
	    'PoSLkL RTRjR RRUlU RTOTR R[L[R RbLbR RVDhDhH RTOjOjR RVDVHeHkJ R[ISJ R\\SWWk[ RdV_YS[',
	    'Po`RhR RRTWT R\\ZjZ RRERW RREWEXT R[F[I R\\T\\Z R`K`R RcScZ RhOhR RjTj[ R[FkFkI RaHeJ RiLlO RbCdF R\\K[O RhIcNZR',
	    'PoZIlI R[LjL R]ShS RRSXS RRFRU RRFXFXS RcCc[ R\\EjEjL RjNj[ RbSgX RcS]Z R^O_R RgNeR R[N[TZY',
	    'PoZFlF R[JkJ RZNlN R[SkS RRSXS RYWlW RRFRV RRFXFXS R]J]S R_C_H RcJc[ RfCfH RhJhS',
	    'Po[IkI R\\PjP R\\SjS RRTXT R\\WjW RRGRV RRGXGXT R[E[I R\\L\\V R`E`I RfEfI R\\LjLjV R[EkEkI RgXl[ R_XY[',
	    'PocIkI RRTWT RRFRW RRFWFWT R`W`[ RiDjG R_OZT ReO`T RdVf[ RiVk[ RfCgLjT R[M_N R]EYN R]JaJ^Q R\\VZ[ RgIcR RfHiR R]GbG_O RfFeO',
	    'Po\\GiG R\\KiK RYNlN R[QaQ RRSXS R[UaU RRFRU RRFXFXS R[N[X R\\D\\K RaNa[ R\\DiDiK RfUk[ RiUd[ ReRgX RcQkQhX R^XYY RaX[Y',
	    'PoYFdF R[JcJ R[RcR RRTWT RRERV RREWEWT R[M[R R_C_J R[McMcR RkDfJ RkLfQ R\\T\\W RaT_X RlTgYc[ RdWYY',
	    'PoRTXT Rd[i[ RRERW RREXEXT RZNZR RZNlNlR R^HaK RiIkL RkEjH RbEaH R[E[H RdEdH R\\KZL RfKcL RaV_YZ[ RcPaX R^D]K RhDgJ R[SjSi[',
	    'PoYHlH R[KjK RZQ_Q RfQjQ RRSWS RZTjT RfVjV RRFRU RRFWFWS RZMZW_V_[ R_M_Q RbCb[ RfMfQ RfVf[ R[EjEjK RjMj[ RZQYY',
	    'Po[QkQ RRTWT RRFRV RRFWFWT RdMjLkN RdGgJdM RlXk[ RZG]J[N RfOiP RfYj[ R[T`W R\\D[H R_F\\L RgDeH RiFgK RiSeX_[ RbCbJdW R[QZY R_K`NZN',
	    'Po[IlI R^RkR RRSXS RhWjY R_XjX Ri[l[ RRFRU RRFXFXS R]T][ R^NkNkR R[ElElI R]TlTl[ R^N^R RiK_L ReKeX R[E[QZUW[',
	    'PoZFcF R[NcN R[RcR RRTXT RYVdV Rh[l[ RRFRV RRFXFXT R[J[R R_C_J R_R_[ R[JcJcR RfElEl[ RhQjS RhJjL RfEfRdY',
	    'Po\\IiI RZLlL R]QiQ RRSXS R]TiT R]WiW RRFRU RRFXFXS R\\E\\I R]N]W RcCcL R]NiNiW R\\EiEiI RfYlZ R`YY[',
	    'Po^GcG RRSXS R\\SjS R\\WjW Rf[j[ RRFRU RRFXFXS R[E[L R\\P\\X RdOdW R[QjPj[ ReCeKlJlK R^C_KZQ R^MeMcQ RZZX[ RkFeG RcJYL',
	    'Po\\FjF RZIlI R[MaM RdMkM RcQlQ RRSXS RZTlT Re[l[ RRFRU RRFXFXS R^K^Q RcCcI ReTe[ RhKhQ RlXl[ R`T^YX[ RaPZQ',
	    'PoYGcG R[KaK R[PaP RRSXTcU RRFRU RRFWFWS R[C[U RaCaU RdMkMi[ R`VaY R[WW[ RkDdFdScY',
	    'Po\\HjH R\\KjK R\\NjN R\\Q`Q RfQjQ RRSYS R[T`T RfTkT RZWlW RRFRV RRFYFYS R\\E\\N RcCbF RcNc[ R\\FjEjN',
	    'Po\\GlG RYMlM RRSWS RYTlT RRFRU RRFWFWS R]F]T R_W`[ R`G`T ReGeT RiGiT RhWlZ R[XW[ R]GYK RdWe[ R]C[I',
	    'PoZGlG R]PhP R]ThT RRTWT R_[b[ RRFRW RRFWFWT R]L]T RbTb[ R\\MiLiT RhWkZ R[I^K RkIhK R]VZZ R`H[N RbC_K RcGjMlN',
	    'Po[EdE RZHkH R^OgO RRTWT R_XfX Rg[j[ RRFRW RRFWFWT R[Q[[ R_T_Z R_TfTfX R]LhKgO R[QjQk[ ReHjM R`HZM RaE^K^O RhDaE',
	    'PoZFlF R[IkI R\\OiO RYSlS RRTXT R\\ZjZ RRFRV RRFXFXT R\\V\\[ RcCcI R\\KiKiO R\\VjVj[ R^P_S RgPfS R\\K\\O',
	    'PoTH]H RaHkH RUMiM RUPiP RRSlS RVZhZ RTDTH RUJUP RVUV[ RTD]D]H R_J_P RaDaH RVUhUh[ RUJiJiP RaDkDkH',
	    'Po[IaI ReIkI R\\PjP RRSXS R\\SjS RYWlW RRFRU RRFXFXS R[D[I R\\L\\S R[DaDaI RcLc[ ReDeI R\\LjLjS ReDkDkI',
	    'PoZDlD R[LkL R\\QjQ RRTXT R\\TjT RZWlW RRFRW RRFXFXT R[G[K R\\N\\T R`D`K RcTc[ RfDfK R\\NjNjT R[GkGkK',
	    'Po\\MjM R\\QjQ RRSXS RZUlU RRFRU RRFXFXS R\\E\\M R`Wa[ RcEcT R\\EjEjM RhWlZ ReWf[ R_G`K R\\W[[ RgGfK',
	    'Po[HaH RdHkH R[LbL RdLkL RZQkQ RRSXS RYVlV RRFRU RRFXFXS R\\D\\L R_N_U R[DaDaH ReDeL RfNfU RdDkDkH ReXl[ R`XY[',
	    'PoZElE R[I`I ReNjN RYRlR RRTXT RRFRV RRFXFXT ReNeR R_V_[gZf[ ReEeIjIjN RaTdW RhVlZ R[VZZ R[I[N`N`R R`E`I',
	    'Po]GhG RdNjN R[PjP RRRXR R[SjS RYVlV Ra[f[ RRFRT RRFXFXR R[K[S ReGeM RfSf[ R[KjKjS R\\W`Y R_DgElI R]FYJ R`G`L^N',
	    'PoZJlJ RRTXT R\\ViV R\\ZiZ RRFRV RRFXFXT R\\Q\\[ RcDcP RcQcZ R\\QiQi[ R]F^I RhFfI RcJgNlP RcJ]OYQ RhC]E',
	    'PoRSWS RYUkU RRFRU RRFXFXS R^C^M RbUb[ RhSlV RaN]Q R[QaS RfWlZ RdLbM RkMgM RfObS^U R]XXZ RfEeK RaGZI RbJYL R`EYF RcEkEkM',
	    'Po[EcE RRTXT Rf[j[ RRFRW RRFXFXT RaUaZ R[KhKgO RgUhX RhFgI R\\F]I RdUeY RbFbI R^V\\Z R[OjOiS R[K[TlSk[ R[NZX RiDaD',
	    'Po[FbF RdFkF RZJbJ RdJlJ RhPlP RRSXS R]VjV R]ZjZ RRFRV RRFXFXS R]R][ RhFhO R]RjRj[ RfMcP R[PZQ RbN^O R[F[K ReFdK RhDfN R_I_O R_C^N',
	    'Po[FkF RZKlK R\\QjQ RRTXT R\\TjT R[WkW RY[l[ RRFRV RRFXFXT R\\M\\T RcCcF RcMc[ R\\MjMjT RgGfJ R_G_K',
	    'PoYGcG RcJlJ R[QaQ RRTWT Rh[l[ R[[^[ RRFRW RRFWFWT R[K[Q R^Q^[ R[KaKaQ RiNi[ RlXl[ RiElH RaTaW R^D_G RfCfQbZ R[TYY RfKeV',
	    'PoZDbD R[NaN R[SaS RRTXT RRFRV RRFXFXT R[J[X R^D^I RYJbJa[ RhWl[ RhVc[ RfQjZ RiQeY ReKhX ReJkIhV RfCcO RaWXX',
	    'PobNeN RfSlS RRUYU R`YhY Rh[l[ RRHRW RRHYHYU R\\P\\[ R`U`Z RfPfS R`UhUhY R\\PlPl[ RaQ_T R[IkIjL RbFfHeL R^EjEdG RcJ[N',
	    'PoYGcG RZMaM RRTXT R[[^[ RRFRV RRFXFXT RZIZM R^S^[ R^C^G RZIaIbM RhWl[ RZQaP^T RgUb[ RfRjZ RiOeY RfCcO ReJgW ReJkIhV RbUXV',
	    'Po\\PjP RRRXR R]ViV R]ZiZ RRFRU RRFXFXR R\\I\\O R]R][ RcJcO R]RiRi[ R[JkIjO R_L_N RaEgElJ RgKfN R^FZJ',
	    'Po]HkH R]LkL R]PkP RRSXS R]TlT RRFRV RRFXFXS R^G^T R`Wa[ ReFeT R]IZL ReWf[ RiVk[ R_C\\J R\\VZ[ ReDdJ',
	    'Po[FkF R\\IjI R]OhO RRTXT R\\ViV RY[l[ RRFRV RRFXFXT RZLZO R\\R\\V RcCcI RZLkLkO R`X`[ RfXe[ R\\RiRiV',
	    'Po^HgH R\\OiO RRTXT R][a[ Rg[k[ RRFRV RRFXFXT R\\K\\O RZQaQa[ R\\KiKiO RcQkQk[ R]RZU R_UZZ RiTdY RfRcU RcDlH RaEYH',
	    'PoTJ]J RaJjJ RTMjM RRQlQ RTZ]Z RaZjZ RTTT[ RTDTJ RTD]D]J RTT]T][ RaTa[ RaDaJ RaDjDjJ RaTjTj[ R_M_Q',
	    'Po\\JjJ R\\NjN R\\PaQ RRTXT RZWlW RRFRW RRFXFXT R\\F\\N RcOc[ R\\FjFjN RbCbF ReSkT RjQeQ RaS[U',
	    'Po[HkH RdQlQ RRTWT RdWlW RRFRW RRFWFWT RcCcN RdSdW RhOh[ R\\D_G R\\U`V[[ RiDfG RcHeKlM R]O[T R]QbQ_X RcH`KZM',
	    'PocEkE R^MeM RbOiO RRRXR R]WbW RfWjW R[[l[ RRFRT RRFXFXR R]S]W RbQbZ RbJbO RcCcH RfQfZ RjSjW R[HlHkL R[H[QZX RhLaL',
	    'Po^LfL RRSXS R\\TiT RY[l[ RRFRV RRFXFXS R\\O\\T R\\OiOiT RfHlN RiDfH RjHhJ R[G^IZN R_V_Z RdDgJ RfVfZ R[EaE^K',
	    'Po_DeD RVZhZ RVTV[ RVHgHfL RVThTh[ RVLjLiO RaPbR RXFZH RePgR RTEZE_E`H RhEdH RXPWS R]P]S RUOlOkShS RVHUQ RhCaD',
	    'PodEkE RZEbE R[HaH RdHkH RdLlL RRTXT RRFRW RRFXFXT R^E^L RgEgL RaQdR R\\UiUc[ RbNlR RZLbM`PZR',
	    'PoZIlI R[NkN R[QjQ RRTWT RZUlU RRFRV RRFWFWT RaCaI ReCeI R\\D]G RiDhG R_K_N RcNcUfYl[ RgJfN RcU_YY[',
	    'PoZElE R\\KdK RRTWT R]TcT RRFRW RRFWFWT R]N]T RcNcT R^G^J R^Z\\Z RfLkKkP RgGeP RhOfY RhNiY RZEZRXZ RbGaK`T_Y',
	    'Po]JhJ R\\PiP RRTWT Rf[k[ RRFRW RRFWFWT RZSZ[ R\\K\\P RiKiP RZSkSk[ RbGcI R_LfO RbDlI RbP^X RdL^O RaEYI ReUfX^X',
	    'PoZHlH R[KhK RZMbM RdQjQ RRTXT RZTlT Rc[f[ RRGRV RRGXGXT RdMdQ RgRg[ R\\EhEhK RdMjMjQ R^VaY R^M^Q RbQZQ',
	    'PocFjF RYJlJ R]RiR RRSXS R[ZkZ RRFRU RRFXFXS R[L[[ R]E]J RcLcX RcCcJ RkLk[ R^M_P RcQhW RcQ]W RgMfP',
	    'Po\\KkK R\\OkO RZQlQ RRTXT R]WjW R]ZjZ RRFRW RRFXFXT R\\G\\N R]S][ RcHcN R]SjSj[ R[IkHkN R`CZI R^EfEcH',
	    'PoRERW R]U]Z RfLbP R]NYR R_U`X RiTlY RdTbZ R]CZL R_H\\O RfCbK RhHfN RZUY[ RgQg[d[ R^KYL RgJbK R`N`RYR RREWEWSRT RiNkQbQ',
	    'Po[RaR RdRjR RRTXT RRFRV RRFXFXS R[M[R R[MaMaR RdMdR RdMjMjR RhWk[ RfViY R]VaY RcChI R^JgIlJ R]VY[ RfWb[ RfTdZ RbC]IYJ',
	    'PoUFlF R_JlJ R_LkL R_OkO R_RlR RZZiZ RZJZR RZTZ[ R_I_S RfHfR RZTiTi[ R_CbF RZJVN R_I\\M R[GYM RUFUSSZ',
	    'Po`FjF R^JkJ R^NkN R_RjR RRTWT R^UkU R`[l[ RRFRV RRFWFWT RdCdJ ReNeY RZJ\\M RZD\\F R]Q]XY[ RhKgM RaKaM RYQ]Q R^Zc[',
	    'Po\\IlI RfLkL R_NiN R\\QlQ R_SjS RRSXS R_UjU R^ZjZ RRFRU RRFXFXS R^W^[ R^WjWj[ R_EYK RaJ_M R_EfEeHfL R\\I\\RZY',
	    'Po]LhL RRSXS RZUlU RRFRU RRFXFXS R\\F\\R R\\FjFjR R_J`L RbL^Q ReIdL RbCbF RcHcOgP RcVfYl[ RcRaW_YZ[ RfG^H',
	    'Po[IlI RRSXS R\\UgU R[YfY Rg[k[ RRFRU RRFXFXS R[D[I R\\P\\T R`EaJ RbNbX RfEfJ R\\PgPgT R[DlDlI RfVhY R^LYQ R^I\\NlMlUk[',
	    'PoZGaG RcGkG R\\PjP RZSlS RRTWT R_[b[ RRFRW RRFWFWT R^C^N RbSb[ RgCgN RgVkZ R]GaJ RiIlL RgEeJbM R]VYZ R\\IYM RgEjK R^E[L',
	    'Po[IjI R[LjL R[OlO R]QiQ RZUlU RRUXU RRHRW RRHXHXT R[F[L R_T_[ R`C`L ReCeL R[FjFjL RdUgYl[ RkVfX R[OZTYY ReY\\[',
	    'PocFjF RZJlJ R[NlN R]QcQ RRSXS RRFRU RRFXFXS R^E^J R`Q`X RcCcJ RlXl[ RfXj[ RhKjN R]S\\W RcT^Z RiUc[ RjPgX RfKePgY R[N[TYZ',
	    'PoZFlF R\\MjM R\\PjP RRUXU R]XhX Rg[k[ RRHRW RRHXHXU R[S[[ R\\J\\P R`C`H RcJcW RgCgH R\\JjJjP R[SkSk[ RfUhY',
	    'PoUK]K RaKiK RRNkN RUZ]Z RUEUK RVSV[ RUE]E]K RST]T][ RaEaK RaEjEjK R^NlT R^LZQRT RaTjTj[ RaTa[ RaZjZ',
	    'PoSElE RUM[M RbMiM RTPjP RUX[X RbXiX RR[l[ RURUX RUGUM RUG[G[M RUR[R[X R_E_[ RbGbM RbRbX RbGiGiM RbRiRiX',
	    'Po^IgI RZQaQ RdQkQ RZTlT RRUWU RRFRW RRFWFWT RZLZQ R^D^I RZLaLaQ RcRc[ RdLdQ R^DgDhI RdLkLkQ RcSfWlZ RbS_WXZ',
	    'Po[FkF RZJlJ R\\PiP RRSXS R\\TiT RRFRV RRFXFXS R\\M\\T RbCbF R_W_[gZg[ R\\MiMiT R`UdW RhVlZ RgGfJ R[VYZ R^G_J',
	    'Po\\GcG ReGlG RZNkN RRTXT RX[l[ RRFRV RRFXFXT RcNc[ R]S`W RgSlW R^G_K RfGjK RhPeW R]CYK R]PZW RfCbK',
	    'PoZJaJ RZMaM RYQbQ RRTWT RRFRV RRFWFWT RZFZM R]C]F RZFaFaM RhVc[ RfQjZ RiPeY ReGeMhW R`[][ ReJgV ReIkIhV R]M]QZY R\\TaT`[ RfCcN',
	    'Po[IjI RZKlK R\\SiS RRTXT RRERW RREXEXT R[D[I R\\N\\S R^V^[ R`D`I ReDeI R\\NiNiS R[DjDjI RdUhYlZ RcSYW RkUfW RdZ]Z',
	    'Po`EfE R[EcE RRSXS RRFRU RRFXFXS RZJZL R`QgPgQ RZJkJkL RhMkP RiGfJ R^UYW R`R[V R]NZP R_M`Q R\\F]I RbFbI RaLeM R^TaXdYk[ R^ThTeWaYZ[ RiDbD',
	    'PoZGlG RcMlM RcPkP RRTXT RcUkU RcZlZ RRFRW RRFXFXT RcKc[ RhLhZ R]QZT R_VY[ RbCcG RhIiL R\\IZO R_L\\R RdIaO R`R]X R`SYT R_NYN',
	    'PocEjE RbNkN R]PkP RRSXS RRFRU RRFXFXS RcIcM RcCcH RfTkY RePhW R[HkHjJ RfP]S RcT]V RjRfT RcW\\Z RaRdTeWeZb[ RdK]K R[H[RZY RfJ`K',
	    'Po^HhH R\\QjQ RRSYS R]WiW R]ZiZ RRFRV RRFYFYS R\\K\\Q R]S][ RcKcQ R]SiSi[ R\\KjKjQ R_M_O RdCfFlH RgMfO RbC_GZI',
	    'PobEjE R]KgK RaMiM R^RjR R]SkS RRTWT R\\WlW RRGRV RRGWGWT R^N^R RaHaM RbCbH R^NjNjR R[HlHkK RdVfYl[ RdScWaY\\[ R[H[QYX',
	    'Po[EkE RZJlJ R\\PjP RRSXS R\\TjT R\\WjW RRFRV RRFXFXS R\\M\\W R^G^K RcCcH RhGhK R\\MjMjW RfXl[ R_XY[',
	    'PoZFlF R\\KiK R\\MiM R\\PiP R[SlS RVZiZ RVUV[ R\\H\\P RbFaH RVUiUi[ R\\HjHjP RTDXF R]D_E RgCeE RSGWI RRLVLWPRR RXR`R',
	    'PoZFlF R\\LjL R\\OjO RRSYS R\\YhY Rf[j[ RRFRU RRFXFXS R\\I\\O R]S]Z R_C_G RgCgG R\\IjIjO RbUeV R`OYV RcS_W R]RkRj[',
	    'PoYGbG RXMbM RRTWT RZUaU RZYaY RRGRW RRGWGWT RZPZZ R[H[L R]C^F RZPaPaY RhRl[ RfRcZ ReHkHkM R`H_M ReCcO RgKhW RgLeW',
	    'PobElE RdMkM RdRkR RYRaR RRUWU RdVkV RRFRW RRFWFWU RYKYT RaKaT R^C]YaXaY RdIdV RgEfI RdIkIkV RiXlZ ReXb[ RaFYG',
	    'Po]OiO RRTXT R\\VkV R\\ZkZ RRFRW RRFXFXT R[H[K R\\R\\[ RcRcZ RcCcG R]KiKiO R\\RkRk[ R[HlHlK R]D^F RjDgF R]K^O',
	    'PoYGbG RdMlM RYMbM RYRbR RRSWS RRFRU RRFWFWS R[H\\L R^C^G R^M^[ RiMi[ R]SaV R^P\\UWY R`H_L RkEdFdRbY',
	    'Po\\JjJ R\\NiN RZQlQ RRTWT R^VgV RZ[k[ RRFRW RRFWFWT RZFZI RcSc[ RZFkFkI RfHeRkW RbDdF R`H`QZW',
	    'PocGlG R[LaL RcNlN RcTlT RRTWT R\\YaY RRFRW RRFWFWT R[E[R\\[ R[EaEaK R\\QaQaY ReIeM RgNg[ RgDhG RkIhN R[MYX',
	    'PoaFlF RRF_F RSI^I RTO]O RRS_S RTZ]Z R`ZlZ RTKTO RTUT[ RVPVS RYCYI R[P[S RTU]U][ RTK^K^O RgGgX RhLlP RdM`Q RgFbP',
	    'PoZFlF RZMkM RYQlQ RRUWU R\\WiW R\\ZiZ RRGRV RRGXGXT RZFZH R\\T\\[ R\\TiTi[ RbJdL RbCcF RfNeQ R_N^P ReHjJ RaH[J',
	    'Po[OkO RRTXT RZVlV RRFRW RRFXFXT R[DaDaL RcUc[ RcDjDjL R^RaT RfRkT R[G^H RdFgH R_P[T RhPdT RaI[L RjIcK',
	    'Po_LfL RYRlR RRSXS RZUlU R^[c[ RRFRU RRFXFXS RZFZI R_I`L R`N`R RcCcF RcUc[ ReNeR RZFkFkI RaHdI RiIkL R\\JZL R\\N[R R[NjNjS',
	    'Po]PkP R]SjS RRTXT R]VjV R]ZlZ RRERW RREXEXT R]O][ R[DbDbL RdNdZ RdDkDkL R\\F^H ReFhH R^M]QZS RaI[L RkIdK',
	    'Po[DbD ReHlH R[LbL R[PcP RRTWT RY[l[ RRFRW RRFWFWT R[D[P R\\S\\[ RaSa[ R[HbHbL ReSe[ R\\SjSj[ RgKjO R_L_Q R_D_H RfCdM',
	    'Po^JkJ RRTWT R`[l[ RRGRW RRGWGWT R^D^J RbDbJ RdJdX RgDgJ R^DkDkJ RZE[H RgRkV RhLkP RYN\\N\\XX[ RbR^V R\\Xb[ RaL_P',
	    'PoZElE R\\JkJ R\\MjM R\\OjO R\\RkR RRSXS R[UiU RRFRV RRFXFXS R]I]R R`C`G RcHcR RfCfG R]IYM R^WcYj[ RhVcY[[ RbYl[ ReXY[',
	    'Po\\EkE R]JaJ RfJjJ R]MaM RfMjM R[PlP RRTXT RRFRV RRFXFXT R[H[K R\\T\\[ RbSb[ RdEdN RfTf[ R[HlHlK RbPaW R\\TkTk[i[',
	    'PoZElE RZJlJ R]PhP RRRXR R_XgX Rg[k[ RRFRU RRFXFXR R[R[[ R]L]P R_C_H R_U_Z RcGcJ RgCgH R_UgUgX R]LhLhP R[RkRk[',
	    'PoYGaG RcGkG RXLaL RcLlL RRSWS R[[^[ RRFRU R]C^L^[ RjOlV RcObV RgChLh[e[ R`OaS R[L[RZX ReLeSdX RRFWFWS',
	    'PoZElE R]JcJ R]McM ReMlM R\\ScS R\\VcV Ra[c[ RRFRU R\\P\\[ R]H]M R]HcHcM R\\PcPcZ RjHkK RhGhRk[ RhMeZ RhKjY RZEZQXY RRFWFWSRS',
	    'PodIlI RYJcJ RYPcP RRTWT RYTbT Rf[j[ RRERW RREWEWT R[K[O R]C]J R^P^Y R`C`J RjDj[ RYEZH RbEbH RaL_P RfNfT RbYXZ',
	    'Po\\HiH R\\JiJ R\\LiL R[QkQ RRSXS R[TkT RYVlV RRFRU RRFXFXS R[N[T R\\E\\L RcNcT RfVf[ R\\EiEiL R[NkNkT R]ZX[',
	    'PoZElE R[HkH R\\MiM R[OjO RYRlR RRSWS RbUlU R[Y`Y Re[h[ RRFRU RRFWFWS R[T[Z R[T`T`Y RcCcH RbMbP RiSi[ RZKlKkM RdWeY',
	    'PoZFlF RRSXS R\\WiW RRFRU RRFXFXS RcCcF RcKcO RfJfO R\\SiQi[ R`GaI RiIlO ReGdI R\\I[N RkIfI RZI`I_O]O RiNeN R\\P\\U\\Z',
	    'PoZHlH R[LiL RYOlO RRTWT RW[l[ RRFRW RRFWFWT R[U[[ R`U`[ R_P_S RbCbO ReUe[ R[EiEiL R[UjUj[ RhPlS R\\QYT RdPeS',
	    'PoZFlF R]KhK R]PiP RRTXT RRFRV RRFXFXT RZMZP R]H]K R]HhHhK RZMlMlP RaRfUlZ RbCdE RbQ\\T RcS[W RiRdTeW[Z Re[b[',
	    'PoZFlF R\\NiN R\\QiQ RRSWS RcWhW R`[l[ RRFRU RRFWFWS R\\K\\Q RcKcQ RcCcI RcSc[ R\\KiKiQ RZIZJkIkK R\\WX[ RZSlSkV R^Zf[',
	    'Po^LhL R\\OjO R]QkQ RRUXU R]WiW R]ZiZ RRHRW RRHXHXU RZGZJ R\\N]Q R]T][ R^I^L RcCcG R^IhIhL R]TiTi[ RZGlGlJ R]D^G RiDgG',
	    'PoZGlG R[PkP R[SkS RRTWT RZVlV RRFRW RRFWFXT R[J[P R_X`[ R[EdEcU R[JkJkP RhWl[ RdWe[ R[XY[ R_L_N RfLfN RjDbD',
	    'Po[FlF ReJlJ R]JcJ RRTXT R^VfV RRFRW RRFXFXT RaHaQ RhHhQ R`KcN RbCdE RcR_V RhIkO RhIeO RaI^P RiTeV^Z R[F[RYZ RhY_Z RjWkZaZ',
	    'Po_PgP R[TkT RRUWU R_[c[ RRFRW RRFWFWU RcTc[ R[F[GlFlH RgVkZ R\\N^O ReLkR RbCcF R]LaL[R R^VZZ RdJjJgN R^IZM RdIgN R]JbJ`O',
	    'PoZEkE R\\NiN R\\QiQ RRSXS RcWiW Ra[l[ RRFRV RRFXFXS R\\K\\P RcTc[ RcKcP RcCcH R\\KiKiP RZHZIkHkK R`Q]T R]WY[ RZTiSjS R_Zf[',
	    'Po\\KjK RRTXT R]WiW R]ZiZ RRGRV RRGXGXS R]S][ RdHdN R]SiSi[ R[IjHjN RePeR R]G]NiOlR R`CZI R^EeEcH R\\PZR R`P`R',
	    'PoZEbE RdElE R[HaH RdHkH RZKbK RcKlK R]QiQ RRTXT R\\TiT RRFRW RRFXFXT R^C^L RhChL R_V_[gZg[ R\\NiNiT RbUeX RhVlZ R[VY[',
	    'Po[HiH R[KiK R[NjN RYQkQ RRTXT R_[b[ RRERW RREXEXT R[E[K R_K_R RbSb[ RfKfR R[EiEiK R^U`W RgSlU RbVjY R^SXU RbVZY',
	    'PoZElE R\\JiJ R\\LiL R_SfS RRTWT RRGRV RRGWGWT RZNZQ R\\G\\N R_P_S RbEaG R\\GiGiN RZNkNkQ RhPkS R]QZS RaPdP R`TZW R^VcYk[ R^VgVbY^ZX[',
	    'PocFjF RYJlJ R[RkR RRSXS R[ZkZ RRFRU RRFXFXS R[L[[ R]E]J RcCcJ RkLk[ RfUiX R_UaW R_NaP RfMiP RfLdP R_T^X RfUcX R_L^P',
	    'PoRH\\H RaHkH RTJjJ RTQiQ RTTlT RRZ\\Z RaZkZ RRERH RRWR[ RTJTT RRD[E[H RRW\\W\\[ R^J_M R^Q_T RaEaH RaWa[ RTMhNiQ RaDkDkG RaWkWk[',
	    'Po`KeK R`PeP RYSlS RRTWT RRERW RREWEWT R`F`P RbQb[ RbCaFeFeP RiLfO R\\LYO RbRgWlY RjHhN R]DZJ R^H[N RiDfK RbR^WXY R^JYJ RjJfJ R^M^PYO RkMkPfO',
	    'Po\\KjK R\\MjM R[QlQ RRUXU R\\WjW RZ[l[ RRHRW RRHXHXU R\\E\\J R`R`U RcEcQ RcTc[ R\\EjEjJ RdReT RhRkT RfGeI R]R[T R_G`I',
	    'Po[FlF R[IlI R`LgL R[OlO RRSXS R^TjT R^WjW RRFRU RRFXFXS R^Q^W R`F`L RcCcF RdOdW RgFgL R^QjQjW RfXl[ RaX[[ R[F[QYY',
	    'PoTEjE RVGiG RWMgM RRQ^Q R`QlQ RST]T R`TkT RTZ\\Z RaZjZ RTVT[ RXOXT RTV\\V\\[ R_C_G RaVa[ ReOeT RWJgJgN RaVjVj[ RWJWN',
	    'PoYHbH RRSXS R_[b[ RRFRU RRFXFXS R^D^O RbQb[ R]S_U R\\HaL RgSeV R]IYN RdOlS RcWjZ ReCcJ RhGeM RaN_QXS R`XYY RjOgO RaDYE RdGkGjO',
	    'Po\\RcR RRSWS RZUeU R]ZdZ RZZbZ RXZ`Z RRFRU RRFWFWS RZEZL R_R_Z RgGgT R[V\\Y R_NcOeR RcVbX R\\QXR R^NZR R_EeEeLbM R^DYE RaE`L R]H_L^LXK RlDl[h[',
	    'PoYFbF RdGkG RcMlM RZRaR RcSkS RRTWT RYVbV RbZlZ RRFRW RRFWFWT RZMZR R[C[J_J][ R`C`J RZMaMaR RgCgM RgOgZ',
	    'PoSEkE RVJhJ RTPjP RURiR RRUlU RYWY[ R[O[U R_C_K RdOdU RVGVHhGhJ RSLSMkLkN RbMhN R_UbXfYl[ R[MUN R`UTX RhVcX R_ZW[ RaZ[Z',
	    'PoTH]H RaHjH RRJlJ RTZ]Z RaZjZ RTVT[ RVMVR RTD]D]H RTV]V][ RaVa[ RVMhMhR RaDjDjH RaVjVj[ R^J]M R`QkU RTDTH RaDaH R^O[SRU',
	    'PoZFlF R[IbI RdIkI RRTXT R\\TkT R[WlW R`[d[ RRFRW RZFZV R_G_O R]ReRd[ RgGgO R^IbM RgIlN RbDdF RhIcO R_K[O RRFXFYRX[ RiQaR',
	    'PoYFlF R^IfI R`SdS RRSWS RRFRV RRFWFWS R^C^I R_W`[ R`L`S R`LdLdS RfCfI RhWl[ RdWe[ RZWYZ RYM]L]SXS RkMgN RhJhTkSkT',
	    'Po[DlD R\\KcK RdKkK RRSWS RdVjV R[[l[ RRFRU RRFWFWS R_T_[ R`G`Q RdRd[ R_KbN RhKcP RhGhMkP R`J]P RbG]H RjGdH R[D[RYY',
	    'PocEjE RaJhJ R^JfJ RcLiL R_QiQ RRSXS R_TiT R\\[l[ RRGRU RRGXGXS R^V^Z R_N_S RbVbZ RdNdS RfVfZ R_NiNiS R^VjVjZ R[GkGkJ RcCcGcL R[G[RZY',
	    'PodFlF RYFbF RYLbL RdMkM RdPkP RZRaR RdSkS RRTWT RZVaV RdWkW R^[a[ RRFRV RRFWFWT RZOZ[ R[H[K R^C^G RZOaOa[ RdCdIkIjM ReMe[lZl[ R`H_K',
	    'PoaFlF RbLkL RbOkO RXP`P RRSWS RaTlT Rf[i[ RRFRU RRFWFWS R\\O\\[ RbIbQ RfCfQ RiRi[ RbIkIkQ RXH\\L RcVfX RiDlE R^L`P R\\CZI R^GZO R^R_W RYRXY',
	    'Po[HaH ReHlH R[KaK ReKlK RRTXT R_UbU R_YhY RRERW RREXEXT R[D[[ R`S`Z R[DaDaK ReDeK RhRhY RbM^Q RaNgNfQ RbR_R RdRhR RdVhV ReDlDl[i[',
	    'PoYGaG RcIkI RYMaM RcNkN RYQaQ RRSWS Rh[l[ RRFRU RRFWFWS RZH[L R]C]G R]M][ R_H_L RcEcRjRi[ RcEkEkR RlXl[ R\\QaV R[TWY R]P[V RcZ`[ ReScY',
	    'PobElE RXLaL RcMkM RcQkQ RRSWS R[U]U RcVkV RRFRU RRFWFWS RZFZL RcIcU RgEfI RcIkIkU RhWl[ R]C^UYZ RdWaZ RZOWU RaP]X RaG]G',
	    'Po`ElE RRF_F RSI^I RRS`S RbZkZ RTZ^Z RTUT[ RYCYI RTK^K^O RTU^U^[ RbRb[ RgHgQ RbRkRk[ RhJlM RhFaM RVOVS RTKTO]O[S',
	    'Po[DcD R\\IbI R\\KbK R\\McM R\\OcO R[XcX Re[i[ RURU[ R[U[Y R\\F\\P R\\FbFbK R[UcUcX RfDfP RURiRi[ RSFVI RiIlL R_O^R RVCTG RXHRLXLTQ RfDlDjI RYJWO RlNhO',
	    'Po\\DkD R]JaJ RfJjJ RfMkM R\\MaM R\\RlR R\\TkT RRTXT R\\WkW R\\ZlZ RRERW RREXEXT R[G[J R]P][ RcDcM RePeZ R[GlGlJ R^OZS',
	    'Po\\FlF RfIkI R^QlQ RRTXT RaZjZ RRFRW RRFXFXT RaSa[ RaHaO RhHhO R`UjUj[ R^IbIcM RgIlN RcDeE RiIdN RbR]W RaH_M RaQaV R\\F[RZZ',
	    'Po^EdE R]NcN R[PcP RRSWS R[UbU Ri[k[ RRFRU RRFWFWS R[R[U R^I^N R^C^H R[RbRbU RYHdHdK RiEkH R\\W\\Y RaWaY RlWk[ RgCgQhZ RYHYSXZ RaK[K RjKdL RkNgWcZZZ',
	    'PoRF]F R_IdI RfIlI RTM\\M R_PkP R_SkS R^WlW RT[X[ RTITM RXCXG RXSX[ RTI\\I\\M R_E_I R_L_S R_EdEdI ReLe[ RfEfI R_LkLkS RfElElI RSP\\QXS R]TVU',
	    'PoUGZG RdGiG RUJZJ RdJiJ RVPiP RRSlS RVZhZ RSLSO RVEVL RVVV[ R_M_U RVVhVh[ RdDiDiL RSLkLkO R]D`E\\F RXNTQ R]H`I\\K RZDUE',
	    'PoVH^H RbHiH RUJlJ RXL`L RYS_S RYV_V RVY_Y RVEVH RYOYY R\\L\\O RVE^E^H RWO`O`[ RbEbH RbEiEiH ReUk[ RhUc[ RcPgW RdOkOfX ReLbS RUJUSSZ',
	    'Po[JjJ ReOlO RRTWT R\\WkW RRERW RREWEWT R[F[J R\\R\\V RZGjGjJ RZSkSkW R^O[S RdKdO RgXjY R]QfQdScVd[lZl[ R]EfEbG R_EYG RcV`YY[ R\\K\\NaM[O RkLdM',
	    'Po[GaG RdGkG R[JaJ RdJkJ R[MaM RdMkM RRSWS RYSlS RRFRU RRFWFXS R[E[M R[EaEaM RdEdM RdEkEkM R_OaP RiOlQ ReOcQ R\\NYQ R`Q^T[WkZ RhTeXaYY[',
	    'PoZFlF RdLjL RZNkN R[QjQ RRSXS RYTlT RRFRV RRFXFXS R[L`L_T RbCbF RdHdL RfLfT RcUgYl[ RcTZX RjVfX R^V^[ RdHjHjL R[H[L R[HaHaL RdZ[[',
	    'PohIlI RYMbM RYPcP RRTWT R\\TcT RYTaT ReYkY RRFRV RRFWFWS RYDYL R[G[J R]W]Z R^D^T R_W_Z RYDbDbL ReOe[ RhChO ReOkOk[ RbVbY R`G`K RYWY[',
	    'PofFkF RZG_G RdKiK RYN`N RRSVS RcVlV Re[h[ RRERV RREVEVS RZQZX R_Q_Y RhQh[ RdSdU RkRjU RbHkHkK R[CXJ R]G^XZX RbHbTa[ RfCfNkOdQ',
	    'Po_IkI R_MkM RRTXT Ra[l[ RRERW RREXEXT R_D_I RcDcI RgEgI R_DkDkI RfRkW R[F\\I RZM]M]XY[ RhL_P ReQ`S RgT`W RjPeUfYcY RfPfW',
	    'Po[EdE R[McM RRNXN ReNlN R[QcQ R[VcV RRYYY ReYlY RRQRZ RRERM RREXEXM RRQYQYZ R[I[V R_E^I R[IcIcV ReQeZ ReEeM ReElElM ReQlQlZ RbXc[ R]XZ[',
	    'Po[MjM RZRaR RcRlR RRSWS RZUaU RZWbW RZYbY Rf[j[ RRFRU RRFWFXS RZPZ[ R[J[M RZPaPaT RjOj[ R[JjJjN RdTfX RhElI R\\FXI R\\EbEbH R_J`M ReJeM RhD`E',
	    'PoZEcE RZGbG RZJbJ R\\RjR RRSWS R\\UjU R\\WjW RRFRV RRFWFXS R\\O\\W R\\OjOjW RfIkN RjIeN ReGgK ReGkFhL R^C]I[N RfCdH RfYl[ R\\LbLaN_N R`YY[',
	    'PofFlF R[MaM ReMkM R\\QkQ RRRXR R]TkT R]WkW R]ZlZ RRFRU RRFXFXR R[I[M R]P][ RZF`D`G R[IaIaM RdPdZ ReIeM RfCfG ReIkIkM R^OYT',
	    'PoZElE R\\JkJ R\\LjL R\\OjO R\\QlQ RRTXT R[WbW RdWkW R[ZkZ RRERW RREXEXT R[U[[ R]I]R R_C_G RdHdQ RfCfG RdTkTk[ R]HYL RbTZT',
	    'PobFlF RYGaG RcKkK RYN`N RcNkN RbRlR RYR`R RRSWS RbTlT RXVaV Re[i[ RRFRU RRFWFWS RYJYR R]C][ RYJ`J`R RcHcN RgCgR RiSi[ RcHkHkN RdWfY',
	    'PoZEkE R^HgH R^JgJ RZMkM R[R`R RdRjR RRTWT R[U`U RdUjU RXY`Y RdYlY RbYjY RRFRV RRFWFWT R[P[Y R^E^M RYPaP`[ RdPdY RgEgN RcPkPj[',
	    'PoTH]H RaHjH RRJlJ RVNiN RVQiQ RTZ^Z RaZjZ RTWT[ RTDTH RTD]D]H RTW^W^[ R_J^L RaWa[ RaDaH RVLiLiS RaDjDjH RaWjWj[ RVLVSfTlU R[TRU',
	    'PoYGbG RcIlI RdNkN RXSbS RRTWT RdTkT RdYlY RRFRU RRFWFWT R]P][ RdGd[ RiGiY RZK\\L R_KaM R]SWY R]VbW R[IXN R_H]N RiDhK R]C]G ReDbM',
	    'PoXFcF ReIlI RZNaN ReNlN RRTWT ReTlT ReZlZ RRFRW RRFWFWT RYRY[ RZHZN RbHbN ReGe[ RiIiZ R]J`L R`H[M RhEiH R_T`W[V R^N\\W RfCcM RYRbRb[`[',
	    'Po_IkI RSIWI RYI]I R_MkM RSO\\O R_RlR RSS\\S RRW]W RSDSI RSLSS RWDWI RXLX[ RYDYI RSL]L]S RYD]D]I RbRbZ RhJhR R_DkDkI RgWk[ RkThW ReRiY RSDWE RgZ`Z RcJcPbV R_D_R^Z',
	    'PoZEbE RdElE R\\LiL RbPhP R^QeQ RRUWU RZZiZ RRFRW RRFWFWU R[M[R R_C^F RcGcP RcRcZ RgCgF R\\T\\WiWl[ R\\TjTjX RfJiJ R\\G]J`I\\J RZMkMkRiR R\\GiGiJfI R_JcIfI',
	    'PoZDaD RdDlD RZMlM RZTkT RRTXT R]VbV RRERW RREXEXT R[G[K R]S][ R`M`S R[GaGaJ RdGkGkJ RZPkPkS RfMe[lZl[ RcJcM RhHhJ R^H^J RkVeW RbZ\\Z ReGdK RZMZSYZ',
	    'PoXElE RZIaI RYKbK RYObO R\\RcR RYR]SiS RRTWT RXVlV RRFRV RRFWFWT R]N]R R^K_M RfCfN RcJjJkPlR R^C^G]K R_V[[ R\\KZN RcMhO RY[`[c[hYkZ RbM_M',
	    'Po[IjI RcOlO RcRkR RYTaT RRTXT RcVkV RcZlZ RRFRV RRFXFXT R[E[I R^T^[ R`E`I RdMd[ ReEeI RhMhZ R[EjEjI R`QaT R_M^P RZN^PZT R\\K[O R`VaY RZVYZ ReKbR',
	    'PoSEkE RVIhI RVO]O RaOhO RTQjQ RUSiS RRUlU RZOZU RVMVN]M]O R_C_K RdOdU RVGVHhGhI RaMaNhMhO RSKSLkKkM RYWY[ R`WeYl[ RiWcX RaURX R_ZX[',
	    'Po[FaF RdFkF RdIlI RZIbI RhMkM R\\QiQ RRSWS R\\TiT R\\WiW RRFRU RRFWFXS R\\O\\W R^C^I_M R\\OiOiW R[DZH RdDcG RfXl[ R]I[N ReIdM RbL^L R_YY[ RgEhM',
	    'PoYFlF R[J_J RcOlO RZQaQ RRTWT RZUaU RRFRV RRFWFWT RZLZT R\\W\\Z R^M^T R_C_H RYNaMaT ReCeH RhJh[ R^W_Z RaVbY RgNlX RgNcW R\\HYN RYWX[ RjIcJ',
	    'PoRF^F R`FlF RSI]I RaIkI RTP]P RaPjP RRS^S R`SlS RTZ]Z RaZjZ RTVT[ RXCXI RTL]L]O RTV]V][ RaVa[ RfCfI RaLjLjO RaVjVj[ RhQgS R[QZS RVQWS RcQdS RTLTP RaLaP',
	    'PoZP^P RaPjP RRSWS RZU^U RbVjV RbZjZ RRFRU RRFWFWS RZEZU RZE^E^U RaHaP RbSb[ RfIfP RbSjSj[ RZK_JkIkP R^W_Z RZWW[ RcLdN RdEhEjJ RhKhN RbF`J RdI]J',
	    'Po_FlF RRF^F R`IkI RUI[I RSO]O RSR]R R_SlS RRU^U R`ZkZ RSKSO RUCUI R[C[I RSK]K]O R`U`[ RbOcS ReCeI RaKjKjO R`UkUk[ RZW\\Z RXIWV[Y RWWS[ RaOiOhS RaKaO',
	    'Po^EcE ReKlK R^LcL R[NdN R\\RcR RRSWS RaVdV R]XbX RRFRU RRFWFWS RZGZU[[ R_C_L R_X_[ R`TaV R\\P\\QcPcR R_T]V RjEkH RZHdHdJ RhChSe[ R[TdTc[b[ RhMfY RhKjY RYOXY RbI[J',
	    'Po[HlH R]JbJ RgJlJ R]LbL RgLlL R]QlQ RRSXS R\\ZhZ RRFRU RRFXFXS R]N]Q RbNbQ RbSbZ RgNgQ R^U^VhUgW R[DlDlH R]NlNlQ R^WgXi[ R`Q[U R[D[TW[ ReHdL R^SlSk[ R[NYX',
	    'PoYElE RZKaK RdKkK RdNkN RZNaN R\\PhP RRTWT Rg[k[ RRERW RREWEWT RZIZ[ R^C^G RZIaIaN RbOb[ RdIdN RfCfG R]RgRgV RdIkIk[ R]VdVgX RbU^Z R]R]V',
	    'PobNiN RRSWS R[UjU R[ZjZ RRFRU RRFWFWS R[P[[ R]C]L R^M^P RcLcP RYPkPj[ ReXhY R_SaS R_WaX ReShT RfHbL R_Q\\T R`U\\Y RfQcT RfVcY RfEdK RkLgL RaGYH RaJYK R`EYF RcEkEkL',
	    'PoYEkE R[HhH R[NaN RdNiN RZQkQ R[SjS RRTVT RYUlU RRERW RREVEVT R^W][ R_P_U R[L[M`L`N RbCbJ R[F[GhFhH RdLdMiLiN RYJYLlJlM RjVfX RePdUgYl[ RbUZY RcZ\\Z',
	    'PoZDaD RdFlF RZJaJ RZMaM RcMlM R\\RjR RRTWT R\\TjT R\\WjW RRERW RREWEWT RZDZM R\\O\\V R^D^G R^J^M RZGaGaJ RcJcM RfJfM RiJiM R\\OjOjV RcJlJkM RfWe[lZl[ RfGhH RdCbH R_W_YY[',
	    'PoZFaF RcFlF RZMkM RbQlQ RRSWS RcSkS RcWkW RcZlZ RRERV RREWEWS RZWXZ RZIZM R]W]Z RcOc[ ReIeM RhOhZ RZIjIjM R^E^H R\\DXG R]MYQ R_W`Y R_OYU`T`V RdCbG R_I`M R_PYQ',
	    'PoRZlZ RRER[ RRElEl[',
	    'PoXPeP RYWfW Rf[l[ RRER[ RYJYV RXJeJeQ RRElEl[ RfTeW',
	    'PoRSdS RRYlY RRER[ RRKdKdS RRElEl[',
	    'PoRZlZ RRER[ RRElEl[ R_H\\QWV R_HbRgV',
	    'PocRkR RRYlY RRFR[ RcFcR RRFlFl[ R[FZOUS',
	    'PoRZlZ RRER[ RRElEl[ RVT]T`TgShV RbNgR R^HXT',
	    'PoWIgI RVOiO RZW_W RRZlZ RRER[ R_M_W RRElEl[ RfI_L',
	    'PoYSeS RRZlZ RRER[ RYKYS RYKeKeS RRElEl[',
	    'PoRYlY RRGR[ RRGlGl[ RcRfU R^C\\G RWLdR ReJ[TUV',
	    'PoULiL RRZlZ RRER[ RRElEl[ R_LbReUiV R_G_MZUUW',
	    'PoUKiK RRZlZ RRER[ RRElEl[ R[QhW ReKbQ_TXW R]GXQaS',
	    'PoVKhK R^WbW RRZlZ RRER[ RbGbV RRElEl[ RbK\\QVT',
	    'PoUKiK R_WdW RRZlZ RRER[ RdGdW RRElEl[ RYN]R',
	    'PoVJ_J RWRfR R_WhW RRZlZ RRER[ RWLWS R_G_W RfLfS RRElEl[ RfH^I',
	    'PoUJiJ RcWhW RRZlZ RRER[ R_F_J RZMdMdV RhThW RRElEl[ RYSUW RZMYU',
	    'Po[W_W RRZlZ RRER[ R_G_W RRElEl[ RbOhU RhJbO R[OUU R_HcR RUK\\KYR',
	    'PobMiM RRZlZ RRER[ RYQYZ RbEbL RYQeQeZ RiJiL RRElEl[ RZKUM R[EZK',
	    'PoUIhI RRZlZ RRER[ R`L`W RRElEl[ RaNhQ RbI]NUR',
	    'PoXIfI RVNhN RRZlZ RRER[ RRElEl[ R[RWV R\\NZT R`UWV RdRfV_U',
	    'PoRYlY RRGR[ RRGlGl[ R`QdRgU R]C\\G RWK`O R`I[NbQ R]MUP RhKbQWW',
	    'PoRZlZ RRER[ RRElEl[ R`Q[V RZITN R\\MVS R]J[O RcJ_S ReWaW RZGXKhJfV',
	    'Po[LcL R[ScS RRZlZ RRER[ R[E[Z RcEcZ RRElEl[',
	    'PoWIgI RUNiN RbWiW RRZlZ RRER[ RbNbW RiTiW RRElEl[ RZSWW R[NZU',
	    'Po`VhV RRZlZ RRER[ RZJZW RaGaV RRElEl[ RhShV R[IUO RgK`N',
	    'PoWIhI RXOgO RVUhU RRZlZ RRER[ R_I_U RRElEl[',
	    'PoVKiK RRZlZ RRER[ R_G_W RRElEl[ R_K]OVU R_KaNgT',
	    'PoRYlY RRGR[ RRGlGl[ R^C\\G RVMgU R_IZOVP R]LgL`SWW',
	    'PoVKiK RUQiQ RRZlZ RRER[ RcGcW RRElEl[ R[G[OZTWW',
	    'PoRZlZ RRER[ RRElEl[ RWIYL R]G_L RVOhV RfJ^SUW',
	    'PoVIhI RWMgM RcVgV RRZlZ RRER[ R_F_X RRElEl[ RUQhQgV',
	    'Po[WeW RRZlZ RRER[ R[N[W RRElEl[ ReTeW R_GcKiM R_FZKTM RdPZR',
	    'PoWPfP RUViV RRZlZ RRER[ R_L_V RRElEl[ R]HcHhM R[IUN',
	    'PoUNiN RRZlZ RRER[ R`I`X RRElEl[ R_MfTiU R_MWU R^IXJ ReHZI',
	    'PoWIgI R_OfO RUViV RRZlZ RRER[ RYMYV R_I_V RRElEl[',
	    'Po[MdM RbVfV RRZlZ RRER[ R]P]X RWPfPfU RRElEl[ R`FcJjM R^F[JTM',
	    'PoVKiK RYVfV RRZlZ RRER[ RYOYV R_G_O RYOfOfV RRElEl[',
	    'PoXLfL RXPgP RRZlZ RRER[ RXHXW RXHgHgL RgTgW RRElEl[ RaReV R_LbT R_UWW',
	    'Po[MgM RWMcM RXVfV RRZlZ RRER[ RXPXV RXPfPeV RRElEl[ RbIgN R]GWM',
	    'PoWIhI RXOgO RVUhU RRZlZ RRER[ R_I_U RRElEl[ RcQfS',
	    'PoRZlZ RRER[ RRElEl[ RZJUL R\\GWL R[KaNiQ R\\RcS RZIfIcL]NUQ RYUeW',
	    'PoUIiI RZPfP RZSfS RbWfW RRZlZ RRER[ R[L[W RYMfMfW RRElEl[ R[KUQ R]GZN',
	    'PoVNiN R`XdX RRZlZ RRER[ R_K_N RcEbKhIhK RRElEl[ R\\SXW R[EZJUL R]N[U R\\ReRdW',
	    'PoYHeH RUJiJ RXMfM RXPfP RXWfW RRZlZ RRER[ RXSXW RXSfSfW RRElEl[',
	    'PoUHiH RRZlZ RRER[ RRElEl[ RbOhU R`IcQ R`HUM R^NUR RhKfMbN R[K`P]RUV R`W\\W R`OaU',
	    'PoUIiI RWPgP RWSgS RRZlZ RRER[ RWMWW R_F_W RRElEl[ RcGfH RWMgMgWcW',
	    'PoWHgH RVOiO RXWfW RRZlZ RRER[ RXRXV RWKeKdO RXRfRfV RRElEl[ R]H[O',
	    'PoWOhO RVShS RRYlY RRJR[ R_O_X RRJlJl[ RYK[M ReKaO R[GbJ RTEiE`I',
	    'PoXKfK RRZlZ RRER[ RWNWT RXGfGfK RWNgNgT RRElEl[ RaThX R_P^T RXGXK R]VUX',
	    'PoZLdL RWSgS RdXgX RRZlZ RRER[ RWOWX R\\O\\W RaOaW RWOgOgW RRElEl[ RaHiL R^G`G R^F[JTM',
	    'PoYJfJ RUNiN R_UcU RRZlZ RRER[ RZQcQbT R[Q[XeWeX RRElEl[ RbLgR ReGhK RZGVK R\\MVS R_G[O',
	    'PoWIgI RULiL RUQiQ RVThT RRZlZ RRER[ R_G_L R_Q_X RRElEl[ RZM[O RdLcO',
	    'PoWHhH RWJgJ RUMiM RXSfS RbXfX RRZlZ RRER[ RXOXX R_G_M RXOfOfX RRElEl[ R_O_S',
	    'PoUJiJ RWR]R RRZlZ RRER[ RWMWR RWM]M]R RhThW RRElEl[ ReQ^W RdVhW RdGhG RfMcT R_TUU R`FbT',
	    'PoWKgK RUMiM RdXgX RRZlZ RRER[ RWHWK RWQWX R\\Q\\W RbQbW RWQgQgW RgHgK RRElEl[ R^M^Q R_G_K',
	    'PoUKiK RXPfP RVShS RWViV RRZlZ RRER[ RWTWW RXMXP RaPaX RXHeHeK RXMfMfP RRElEl[ R^F\\K',
	    'PoXJeJ RXOfO RXRfR RXUfU RRZlZ RRER[ RXMXU RXMfMfU RRElEl[ RcGgK R]GWJ RcWhX R\\WVX',
	    'PoWJhJ RUNiN RZTcT RRZlZ RRER[ RZPcPcT RZXeXeV RRElEl[ RXGZI ReGcI RZTZX R^G\\LVR RaJcNiR',
	    'PoWLgL RVOhO R_WdW RRZlZ RRER[ RWGWL R\\G\\L RbGbL RWGgGgL RRElEl[ R\\SXW R\\PZV R\\SeSdW',
	    'PoUHiH RWMgM RWPgP RUSiS R_XcX RRZlZ RRER[ RWKWQ R_F_Q RcRcX RWKgKgQ RRElEl[ RYT]V RcFfG',
	    'PoWHgH RUKiK RRZlZ RRER[ R_R_X R_F_K RRElEl[ R_RaTiW R_RUW RXNfNfRXRXN',
	    'PoYJeJ RWOgO RWRgR RWTgT RRZlZ RRER[ RWMWT RYHYK RYGeHeK RWMgMgT RRElEl[ R\\UVW RbUhW',
	    'PoWHfH RVLhL RXPfP RUWiW RRZlZ RRDR[ RXRWW R\\R]W RaRbW RXRfRgW RRDlDl[ RcMgQ R_F_L\\MWP',
	    'PoUJjJ RWLfL RWNhN RUQjQ RXUfU RXWfW RRZlZ RRER[ RXSXW R_F_P RXHfHfL RXSfSfW RRElEl[',
	    'PoYJeJ RUMiM R\\UbU RXWfW RRZlZ RRER[ RXOXW RYGYJ R\\R\\U R_J_M R\\RbRbU RYGeGeJ RXOfOfW RRElEl[',
	    'PoWJgJ RULiL R\\UbU RWWgW RRZlZ RRER[ RWOWW R\\O\\W R_J_O R\\RbQbW RWOgOgW RRElEl[ RdGgJ R]GWJ',
	    'PoUHiH RWLgL RWOgO RURgR RUTiT R`XdX RRZlZ RRER[ RWJWO R_F_Q RdSdX RWJgJgO RRElEl[ RYU]W',
	    'PoULiL R\\QbQ R\\TbT RWWgW RRZlZ RRER[ RWOWW R\\O\\W RbObW RWOgOgW RRElEl[ R_L\\O RaGhJ R]GVJ',
	    'PoWJgJ RTLjL RWPgP RWRgR RWUgU RRZlZ RRER[ RWNWT RWGWJ R_F_L RWGgGgJ RWNgNgT RRElEl[ RbVjX R\\VUX',
	    'PoWKgK RWMgM RUPiP RVShS RWVgV RRZlZ RRER[ RWGWK R\\G\\K R_K_P R_S_X RaGbK RWGgGgK RRElEl[ RZQ[S RcQbS',
	    'PoWKgK RUMiM RXRfR RRZlZ RRER[ RXOXR R\\T\\X RXOfOfR RWGgGgJ RRElEl[ R_RbUiX RgScU RWGWK R\\G\\K RaGbK R`RUU R_WXX RaWZX',
	    'Po]GaG R\\JbJ R\\MaM R\\PaP R\\WbW RRZlZ RRER[ RTPYOXX R\\S\\W R\\SaSaW RdRdW RhOhX RRElEl[ RYMZP RTIWL RdJgM RfFeJ RWFUJ RZRZV RiRjV RhJeO RXJVO RURTW RiMjPcP',
	    'Po[HbH R\\JaJ RUM[M R\\QaQ RVThT RRZlZ RRER[ RWNWQ R_R_Y R\\NaNaQ RfNgQ RRElEl[ RgGcJ RhHdM RWGTJ RYHTM R\\N\\Q RiNjP R_SaUhX RTNTQ RdNdQ R_S\\UUX RYITJ RhIcJ R]L`L^L RiLcM',
	    'PoTMjM RR[l[ R_D_[',
	    'PoRK\\K Ra[k[ RWDWV RaDa[ RlVk[ R]TRW',
	    'PoTMjM RR[l[ R_D_[ RdQjW',
	    'Po]FlF RRJ\\J R`[e[ RWDWU ReFe[ R\\SRV',
	    'PoVThT RR[l[ R_P_[ RWF\\LbOkQ RSFhFcL\\ORR',
	    'PoRJ\\J RWDWU RbCb[ RbMlQ R\\TRV',
	    'PoUHjH RROlO Rc[l[ R_C_O RcOcZ RlWk[ RZVWYR[ R[OYW',
	    'Po]DcD RRJ[J RWCWT RgVl[ R_UY[ ReOiY RbO]X RdKgW RbJ_U R\\RRU RcFfU RcGaS',
	    'PoUFlF RYOhO RWZkZ RaHaZ RUFURSZ',
	    'PoRHlH R\\QjQ R[[k[ RXNX[ RcJc[ R]CXNRR',
	    'Po^EkE RRJZJ R\\NlN R`[d[ RVDVU RdEd[ R[SRV',
	    'Po^IlI RRJYJ R][k[ RVDVU RfP]Y R^NiNcS RlWk[ R`C\\L RZTRV',
	    'PoRKZK Rc[k[ RVDVV RcFc[ RlWk[ R[TRW RiD\\G RdO\\P RgO_P RkNbP',
	    'Po]EjE RRJ[J R\\KlK Rc[g[ RWCWU R[SRV R_L^RjQh[',
	    'PoUHiH RSNkN RTTjT RR[l[ R_C_N R_P_[',
	    'PoRJ[J R^OjO R^[k[ RWDWU R^O^[ R]EjEjP RlWk[ R[SRV',
	    'PoRJ\\J R^OkO R^[k[ RWCWU R_E_[ R^EkEkQ RlWk[ R\\TRV',
	    'PoRJZJ R^[k[ RVDVU R^F^[ RdCdV RlWk[ RZTRV RkIbLZN RkHjTgT',
	    'Po^MkM RRN^N R\\[l[ RdCdZ RWWS[ R^DRF RXFXRWW',
	    'PoRKZK R\\OlO RVDVU ReFe[ RZTRV RjD]F',
	    'PoRJ[J RWDWV RfEfX RlDl[ R[TRW R_D_Q^X',
	    'PoRJ[J R]PhP Rd[j[ RWCWU RaC\\M R\\TRV R_HlHlSj[',
	    'PoRK\\K RcMkM RZ[l[ RWDWV RcCc[ R\\TRW',
	    'PoSGkG RUThT RR[l[ R_N_[ RbIgNkP R^D\\IWNRP',
	    'Po[FlF RRJ[J RWDWU RcFc[ RcKlO R[SRV',
	    'PoRK[K R]YlY RWDWV R]I][ RdCdY RlIl[ R[TRW',
	    'Po]GlG RRJZJ RWCWU RcCdF RZSRV R]G]R\\Y',
	    'PoRJZJ Rf[j[ RWDWU RcO]U R`Y\\[ ReS^Z R\\EhEdH RhLdU RcLaQ RZTRV RcI]LlLlQjZ',
	    'PoRJZJ R^LlL RWCWT RfLf[ R^F^SZZ R[SRU R^L\\W RjD^F',
	    'PoVIhI RVNhN RTTiT RR[l[ RVDVN R_P_[ RVDhDhN',
	    'PoRJZJ Re[j[ RWDWT R_SZX RcW_[ RfOaZ R_D[O RbJ]V R[RRV RgIcV R^IlIlRj[',
	    'PoRJZJ RVCVT R^E_NcUgYl[ R]SZ[ RZSRU R^MjMgTbX][ R[EhEgM R^J\\W',
	    'PoRJZJ RVCVU RgNg[ ReFlM RdCeG R^WZ[ RbF[M RbC`H R[SRU R_N_S^X',
	    'PoRJ[J ReNlN RZ[l[ RWDWU R^J^[ ReCe[ R[TRV',
	    'PoRJ[J R]NkN RWDWU R]F][ R[TRW RcY[Z RlWk[iZ ReEeMgX RiD]F',
	    'PoRJZJ RVCVT R_LcThYl[ RZSRU R]LkLhSbX][ RlE]E]O\\UY[',
	    'Po^FjF RRKZK R[MlM Rf[l[ RVDVU RfMf[ RlXk[ R_V[[ RZTRV RaN_W',
	    'PoRJZJ RgLlL R[PiP RVCVT R_DgDgL R_J[N R]P^ScWk[ RiQcW[[ RZSRU R_D_K',
	    'PoRJZJ R_LhL RVDVU RdDkL RbE[L R\\QjQe[ R[TRV',
	    'Po^IlI RRJZJ RWCWT R_LcTgYl[ R`E[P RhKdT`XZ[ R_IaR RhIfR R[SRU',
	    'PoRJ[J R^MgM RWCWU R`C\\M R[SRV RgS]V R_HlHlSj[e[',
	    'PoRJZJ Rg[l[ RVDVU R[E[I R`JgJgZ R[EkEkI RlVl[ R^WZ[ RZSRV R`J`Q^W',
	    'Po\\HlH RRJZJ R]SjS Rd[l[ RVDVU R]K]S RdCd[ RjKjV RlXl[ RZSRU',
	    'Po[HlH RRJ[J Rc[i[ RWCWU RdCdH R[TRV RaNjNjUi[ RaH`Q^WZ[',
	    'PoRJZJ Rc[g[ RVDVU R_W[[ R_J[N RfFjN RaE^M RaR^Y RbO_X RZSRV R]OiOiTh[',
	    'PoaQfQ RVUiU RR[l[ R_Q_[ R]EdEhIlJ R]LZOUQ RYEVIRJ RWJgKfQ',
	    'PoRJZJ R[OlO Re[i[ RWDWU R^EiEi[ RaHeK R[TRV R^E^Q]W',
	    'PoRJ[J RWCWU RfVk[ RcHcPgX R^IkIjN R`D[N RcOaU]YY[ RcMfV R[SRV RcLbT',
	    'Po[ElE RRJZJ RWDWU RdId[ RhMlQ R^OZP RdI\\P ReEcJ R[SRV',
	    'PoTSjS RR[l[ R_D_[ RWI]O RfJlP ReKaO RVLRP RYFTO RgEcN',
	    'Po[HlH RRJZJ Rg[l[ RWCWT RcCcH R^LgLhZ R]WY[ RlWl[ R[SRU R^L^S]W',
	    'PoVH^H RaOkO RTUiU RR[l[ RVCVP R_P_[ RbCbO RkLkO RjFaI R^NRP',
	    'PoTGjG RSLkL RTUiU RR[l[ R_P_[ RdCdP RYNUR RZCZJYO',
	    'PoSG\\G RUUiU RR[l[ R_R_[ RfHcJ RiEfH R_DaHeLlO R[HWMRO R_C_O[P',
	    'PoUUiU RR[l[ RWE`E_Q R_R_[ RhMkQ RfIiP ReEhN RWFVN RgD`E',
	    'PoWDhD RSIkI RcQkQ RUUiU RR[l[ R_Q_[ RcIcQ RkNkQ R[MXQSR R[IZO',
	    'PoRJZJ R[PlP RVDVU R\\HiHiP ReUj[ RaU\\Z RcDcPgX RcP_X RZSRV RcLbU',
	    'Po[FkF RRKZK RY[l[ RVDVV RhQgW R_MhLhS RZURW RaF]TgTf[',
	    'PoSEkE RTUjU RR[l[ R_R_[ R\\KaP RTLYP RdKiP RiGdK RXGTK R`G\\K',
	    'PoUUiU RR[l[ RTETP RZDZQ R_R_[ R_EaJeNlQ R]EiEgIcN\\P',
	    'Po]FjF RRJZJ R[NlN RdYkY RVCVV RhSk[ RbN\\Z RZTRW RgXbY\\Z',
	    'Po]ElE RRJZJ Re[i[ RVDVU RZSRV R_LkLkRi[ R]E]P[X RdGdObW',
	    'PoRJ[J RWDWT R^D^U R^DjDjU RhXk[ ReVhW RcV_Y[[ R[SRV RdHdOcV',
	    'PoRK[K ReMiM R\\UiU Rf[k[ RWCWU RcCbF R[TRV R^FjFiM R^F^PlPlVk[',
	    'Po[IlI RRKZK RVDVU RdCdI R^KbTgYl[ RiIeS`XZ[ R^IaS R[SRV',
	    'PoUViV RR[l[ RTDTR R_R_[ ReLjQ RcFgO RTD[DWI[J RcG`M^PZQ RcDaM RcDdK R[MWN',
	    'PoRJZJ RVCVU RdCdN R_NaTfXl[ R]HlHkL R]NjNhTcX][ RZSRV R]H]Q[W',
	    'PoRJZJ R]MjM R\\TlT RVCVU RdCd[ RgFkJ R`E^J RZSRV',
	    'PoURiR RR[l[ RSISN R_C_I R_M_[ RSIkIkN RVEYH RhEeH',
	    'PoRJZJ R]MlM R]SlS RWDWU R]G]U ReCe[ R]GlGlU R[SRV',
	    'PoRK[K R^LiL R^TiT RY[l[ RVDVV R^E^[ R^EiEi[ R[TRW',
	    'PoRKZK R^MjM R^VjV RZ[l[ RWDWU R^E^U R^EjEjU R[TRV',
	    'Po\\ElE RRJ[J R_ZjZ RWDWT R`M`[ R_OjOj[ RaI\\T R[RRV RbE^Q',
	    'PoRJ[J R`[k[ RWDWU R]H]L R`L`[ RdCdG RlWk[ R]HlHlL RiN_S R[TRV',
	    'Po[IlI RRJZJ R_QiQ R_YiY RVDVU R_C_[ RiCi[ RZTRW',
	    'Po[EkE RRJYJ RZQlQ RVDVU RcEc[ R^H^N RiHgN RZTRV',
	    'PocIlI RRJ\\J R^ZjZ RWCWU R^Q^[ RcCcP R^QjQj[ R[SRV',
	    'PoRJZJ RVDVV RZTRV RhDhJjY R\\E\\QZZ RbEbR`Z RaE\\E RjC_E ReSgZ_Y',
	    'PoRJYJ R\\KkK R`[k[ RVDVU RaNa[ R\\EkEkK RlWk[ RZSRV RjO`T R\\E\\R[Y',
	    'PoRJZJ R^ZjZ RVCVU R^Q^[ R^QjQj[ R[MbMkLlO RgHkL RbC^M R[SRU',
	    'Po\\FlF RRJ[J R[[l[ RVDVV RdIdY ReLlR RcK[S ReFbM R[URW',
	    'PoRJZJ RaThT Rg[l[ RWDWU R]E][ RaKaV RaKhKhT R]ElEl[ R[SRV',
	    'PoRJZJ R[QlQ RVDVU R]H]Q R]HjHjQ ReUjZ RcCcQgX RbU^YZ[ RZSRV RcMbU',
	    'PoRJZJ R\\MkM RVDVU RfCf[ R\\GkGkM R`W[[ RkXhX RZSRU R[RlRkX R\\M[R RaCaR`W',
	    'PoRJYJ RVDVU R[K_P RfRbZ R_D[L RaK[W RYTRV Rj[g[ R`RbWYW RbIlIlUj[ RgCgLeW',
	    'PoUGiG RSKkK RUUiU RR[l[ R_C_K R_Q_[ RcKcQjPjQ R[KZOXQTS',
	    'PoRJZJ R^NkN RZ[l[ RWCWU R^G^[ RfNf[ RiD^G R[TRV',
	    'PoRJZJ R[NlN R\\WlW Rc[h[ RVDVU RaQeT RbHeK RZTRV R^EjEjSh[ R^F^L^P\\V',
	    'Po[ElE RRJZJ R^TdT Rd[i[ RVDVU R^J^U R^JeJeS RiEi[ RZTRV',
	    'PoRK[K R_TfT Rd[i[ RWCWU R_M_V R_MfMfT RaC\\M R[TRV R_HlHlQj[',
	    'PoRJZJ RVDVT RdDdG R^WZ[ RhSl[ R`Q\\Y RZSRU R\\H\\K R`MgMiX R\\HkHkK',
	    'Po\\IlI RRJ[J RWCWU ReDhF RfT`Z RaCaNeVhZl[ RhNdW R_Q[Z R`IcT R[TRV R`I^V',
	    'PoRK[K R]MlM R[[f[ RWDWV R]F]W R[TRW RjD]F RdVZW RfEeLgY RlXk[i[',
	    'PoRJZJ R^LlL RWCWT RfLf[ RbQkU R^F^TZ[ R[SRU RjD^F R^N\\X',
	    'PoRJZJ RVCVU RbWf[ RbKdN RdDlM R^PiPhTcX RcCbG\\M RZTRV',
	    'PoRJZJ R\\NlN R_[d[ RVDVU RdCdG RdNd[ R\\GlGlK R\\G\\K R[SRV',
	    'PoRJYJ R`JlJ Rc[h[ RVDVU R^H^[ RhCh[ R\\KYN RaNdT R_C[M RZSRW',
	    'PoRKZK R[QlQ R^[c[ RVCVU RcEc[ R]I_O RjHgO RZTRW RbE]F RjD_F',
	    'Po^HkH RRJZJ R[PlP RWDWU RfVk[ RcHcQgX RdPaV^YZ[ R_C]L R[SRV RcMdPbT',
	    'PoTJjJ RRPlP RSVkV RS[l[ RXJXU RfJfU RiD_EUF R_E_[',
	    'Po\\IlI RRKZK R[[l[ RVDVV RdDdI R[TRW RhLf[ R_L`X',
	    'PoRHlH RaQkQ RUViV RR[l[ R_R_[ RaHaQ RkNkQ RbDhF RZKVPRQ R\\DZL RhJaNZP',
	    'PoRJZJ R\\JlJ Re[l[ RVDVV ReJe[ RlXl[ ReEhF R^WY[ RjNhR`Y R_Q\\Y RZTRW RaCaL_U',
	    'PodFlF RRJ[J R]RkR RWCWT RdCdJ R]KkKkS R[SRV R]K]S\\Z',
	    'PoRJ[J RbQgQ R]XlX RWCWU R]E][ R]EbEbP RgEgP RgElEl[ R[TRV',
	    'PoeIkI R^JdJ RZJaJ RUUiU RR[l[ RXGXR R_Q_[ ReDiF RlNlR RYFRL RaGcNfQjR RYCWH R`DbM RgIaI',
	    'PoRJZJ R]RfR RfUjU R^[k[ RVDVU R^L^[ R]LfLfR RlWk[ R_C[M R[TRV R^HkHkPjU',
	    'PoRJZJ R]JjJ R]PlP RVDVU R]D][ R]DjDjJ RlWl[ RhZk[ RZTRV RcY\\[ RdLfW',
	    'PoTE`E RSJaJ ReQjQ RUUiU RR[l[ R]E]Q R_Q_[ RdEdM RjCjQ RWEWKVO',
	    'Po`IiI RRJ[J RaUhU Rh[l[ RWDWU R]E][ RaNaV RaNhNhU R]ElEl[ R[TRV',
	    'PoRK[K R^MlM R^SlS R^YlY RWCWU R^G^[ R^GlGl[ RdCcG R[TRV',
	    'Po_GhG RRKZK R]ZiZ RVCVU R]Q][ R]RiRi[ R_F[K R_G`JdOlR R`C]J RgHbOZR RZTRV',
	    'Po[JlJ RRKZK R\\NiN R\\RjR R[VlV RVCVU RcCc[ R]FiFiN RZTRV',
	    'PoUUiU RR[l[ R_R_[ R^GkGhJ RTEYG RbHdMgPkR R_C\\J RaM^PYR RbG`N RZMRO',
	    'PobGjG RRJYJ RVCVU RdC_H\\I RbIeL]P RiHeMaR]T RcTgW^[ RZTRV RcPlPjSeX\\[',
	    'PoVIeI RcPkP RVP\\P R`PhP RRPZP RUViV RR[l[ R_R_[ RhLkP RfFjJ RdK_P RWKRP R[L]Q R]CZFUH RgHcH',
	    'Po[GlG RRJZJ RVDVT RgVk[ ReVhW RhKaR RZSRU RcH]MbQ[T RjPeVZ[ RcCcI',
	    'PoSElE RbNhN RTQjQ RUVjV RR[l[ RTITQ R_Q_[ RbEbM RTIjIjQ R\\E[LXO',
	    'PoUF^F RUIlI RZQiQ RXUjU RV[l[ RZLZQ RaQaZ RZLiLiQ RUEUQSZ RiC`E',
	    'PoRK[K R^MkM R^YlY RWCWU R^G^[ R^GkGkM R^RlRl[ RdCcG R[TRV',
	    'PoRJZJ Rf[l[ RVDVS RfDf[ RlXl[ RfOkT R`V[[ R\\G^L RjGhL RZQRU RaQ]SZT RaDaR`W',
	    'PoRJYJ R`KlK RVDVT R^H^[ RgRjZ RiDkI ReCeQaZ R_DZP RYSRV ReMiY RfKcW ReIhV',
	    'PoRJZJ RZNkN Re[l[ RWCWU RcCcN RfNf[ RlWl[ R^VZ[ R\\E^K RjEgK R[SRV R`N^W',
	    'PoUGiG RSLkL RSS]S R_SjS R^[l[ RXNXZ R_C_L ReNe[ R]YR[',
	    'PoRJZJ RfLkL R\\QkQ RVDVU RcMc[ R`DgDgK RcQjX RcQ\\X R^L\\L RZTRV R`E_K',
	    'PoRJZJ RfNjN R[RkR RWDWT RcNc[ RcQjY R^K[O RbR]XYY RZSRU R\\DfDeIkHjN R`D^L',
	    'PoRJZJ R\\KlK ReUiU Ra[k[ RVCVT RaNaZ RaNiNiT RlWkZ R^IZL R_FfFdJ R[RRV R`C]J]S[Z',
	    'PoRJZJ Rb[k[ RVCVU R\\G\\K RcLc[ RcCcG R\\GkGkK RkXk[ RZSRV RhJ]M RgR\\S RiR_S RkQaS',
	    'Po\\IkI RRJ[J R]OkO R[TlT RVCVU RdId[ R_C`H RhCfH RZSRV',
	    'Po]JjJ RRJZJ R]OjO RWDWU R]E]Z R]EjEjO ReUj[ RcOfW RjSeU R[SRU RdYZZ',
	    'PoUVjV RR[l[ RWGWR R_R_[ RlOlR RXERL R`GcNfQjR RdDhF RjIfM\\Q R`DbM R`HYI RcH\\I RkGgHaH',
	    'PoRIZI R]KlK R_ZkZ RVCVT R_P_[ R_PkPk[ R[RRU R]F]R[X RjD\\F',
	    'Po[ElE RRKZK R^PjP R^WjW RY[l[ RWDWV R^J^W R^JjJjW R[TRW',
	    'Po[ElE RRKZK R[MkM R\\TjT RZ[l[ RVDVV RcOc[ RfIlN RbE^M RZTRW',
	    'PoRJZJ R^LhL R]ZiZ RVCVT R]Q][ R]QiQi[ RcEkL RbF[M RbC`H RZSRU',
	    'PoWHgH RWLgL RUViV RR[l[ RWDWR R_S_[ RWDgDgK RiNdP R^LaOePkR R_PVR',
	    'PoRJZJ RaUhU Rh[l[ RWCWU R]H][ RaMaV RaMhMhU R]HlHl[ RdCcH R[TRV',
	    'PoRJZJ R[NlN Rb[g[ RVDVU R\\G\\K RdCdG RgJg[ R\\GkGkK R^QaU RZSRV',
	    'PoTEjE RRPlP RTUiU RR[l[ R\\E\\O R_Q_[ RbEbO RVHXM RhHfM',
	    'Po\\IlI RRJZJ R[QlQ RWDWU RhIh[ R_WZ[ R_C`H RiCgH R[SRU R`I`R_W',
	    'PoRG\\G RTRWR RUUiU RR[k[ RWCWR R_R_[ R^GhGiP RaN]Q R^JbLeN R[KRL RlOkRiQ RbEaN',
	    'Po\\JkJ RRK[K R[RlR RVDVU R`D`R RhDhR RgUkZ R`U[[ R[TRV',
	    'Po[FkF RRKZK RZ[l[ RVDVW RaFa[ RfFf[ R\\L^T RkLiU RZURX',
	    'Po[GlG RRJZJ R_MgM R[PjP Rb[g[ RVCVU ReHkN R`I[N RbC^K RZSRU R_P^UhTg[',
	    'PoaJlJ RRJZJ Rb[l[ RVDVV R\\E]I R[M^M^X[[ RfCfLkU R^Xd[ ReMbU RZTRW RfHeR',
	    'PocGjG RRJZJ R[LlL R[PlP RVDVU RcCcL RcPc[ RcSkV RZSRV',
	    'PoRJ[J R]SlS R\\YlY RWDWU RdCdN R\\NlNl[ R^E`K RkEiK R[TRV',
	    'Po]JjJ RRJZJ R]SjS R^[k[ RWDWU R]E]I R^S^[ RcCcI R\\NjNjS RjEjI RlWk[ RZTRV',
	    'PobEkE RRJYJ RaLlL Rd[h[ RVDVU R_L_[ RiLi[ R_D[I R_KZP RZSRV',
	    'Po[HlH RRJYJ R\\YkY RVDVU R\\K\\Y RkKk[ R_LfT RdQ^W RbCdH RgJcS RYTRV',
	    'PoRJZJ R[NlN RVCVU R\\G\\K R\\GkGkK RbDdE R]TjZ RbI^S RhNfUcX\\[ RZTRW',
	    'Po\\GkG RRJYJ R[NlN R\\TkT RVDVU RcNc[ RbDdF R_H`L RhHeN RZSRV',
	    'Po]IjI RRKZK R]MjM R]RjR Rf[l[ RVCVU R]D]R RgRg[ R]DjDjR RlXk[ R`V^YY[ RaR_X R[TRV',
	    'PoRJZJ R^KiK R\\QlQ Rf[l[ RWDWU R\\G\\J RgQg[ R\\GlGlJ RlWl[ R_W[[ RcCcG RaQ_X R[SRU',
	    'PoRJYJ R[McM RZScS RVDVV ReEe[ RjNlT ReFlEiN RcG[H RYURV RkWhW R_C_R^X',
	    'Po]EbE RRJZJ R\\SlS Ra[d[ RWDWU RdQd[ R^G`K RcGeK RjFhK R^MjMgOdP RZSRV RjC`E',
	    'Po]JjJ RRKZK R]PjP R]UjU RVCVU R]E]U R]EjEjU RfXlZ R`XZ[ RZTRV',
	    'PofJjJ RUWiW RR[l[ RXJXS R_T_[ R_DfDfJ RYHTM R^H[K RXESG R]LaPeRkT R\\LhLdPaR[S R_D^J',
	    'Po]JlJ RUVjV RR[l[ R]E]O R_R_[ RfJfS RXMSR RTDXE RRHWJ RiD]E',
	    'Po^HjH RRKZK R^MjM R]PkP R[UlU RVCVU R^D^L RdPd[ R^DjDjL RZTRV',
	    'Po\\FlF RRKZK Rb[e[ RWCWU RePe[ R`JiJeN RdCeF RaMgO R_PkPiU RZTRV R\\F\\R[Y',
	    'PoRIlI RdSlS RUViV RR[l[ RSMZLZT RUE_E_[ RdKdS RlQlS RjMcN RZQRR RhD_E',
	    'Po\\HjH RRJZJ RVCVU R\\D\\H RjDjH RaOdQ RfXe[ RdCcJgMlO R\\SiSgX RcI^NZP RZSRV',
	    'Po[ElE RRJZJ R\\MkM R\\RkR RVDVU R\\I\\R R\\IkIkR RcU^Y R]TaXZ[ R`Xj[ RZTRV RcEcQbV',
	    'Po\\JkJ RRJZJ R^NkN R\\SlS RWCWT RgElJ RdNdSfWhYl[ RaC_I R`K]Q RZSRU RcU`Y[[',
	    'PoRKZK R^QjQ R^TiT R\\WkW RVCVU RdNd[ RbD\\J R`GdLlO RaFiFdL\\O RZTRV',
	    'Po[GlG RdShS RUWjW RR[l[ RSDSS R_T_[ RXKZN RcCdG R`N[S RSEYEWJ RbG_P RYPWP RaKjKhR',
	    'PoRJZJ R^OlO R]TlT Rg[l[ RWDWU ReJeT R\\KlJl[ R_G[K R`FhFeJ R[SRV R`C^J]S\\X',
	    'PoRJZJ R]PlP R]UlU Rh[l[ RVDVU R]K][ ReKe[ R]KlKl[ RaGiK R]DlEdH R[SRV',
	    'Po[IjI RRJZJ RVDVU RfIeNkMkO RfEkJ RaP[V R_ScXk[ RaC_I^N[O RZTRV R_RiRdW`YZ[',
	    'Po[HlH RRKZK RVCVU R]M`P ReUjZ RhNlQ RdCcQfX RiJfQ R^K[Q RcP`V]YY[ RZTRV RcLbT',
	    'PoUHjH R\\S_S RUWiW RR[l[ RUDUH R_C_H R_T_[ R_I_R RjDjH R_KdPkS RTL\\LXQSS RgLcN',
	    'PoRJZJ R]KjK R]PjP R\\UkU RZ[l[ RVDVU R]E]P RcEc[ R]EjEkP RZTRV',
	    'PoRJZJ R]KjK R]PjP RWCWU R]G]Z RcCcG R]GjGjP RdUjZ RcPfW RiTeV R[SRV RdX[Z',
	    'Po_JkJ RRK[K R^QlQ R^UlU Rh[l[ RWCWU R^M^[ R_D_J R_DkDkJ R^MlMl[ R[TRV',
	    'Po\\HlH RRJYJ RVDVV RgDjE R\\MbMbV^V RYURW R\\H\\RZY ReCeMgYk[lW RjLgUb[',
	    'PoRJYJ RgMkM RbVkV RbZlZ RVDVU RcJcV RgEgV R]WX[ R^R[Y RYTRV RkDaF RZE_E[N_M]W R[Q]V`YgZ',
	    'Po[HlH RRKZK R]LiL RVCVU R[O[R R]EiEiL R[OkOkR R_ScXj[ R]RiRdX][ RZTRV',
	    'PoSG]G R`JlJ RTSXS RUViV RR[l[ RXCXS R_S_[ RgJgS R`E_K_P RjD_E R]KRM',
	    'PoRJZJ R[PlP Rc[g[ RVDVU RhMh[ R^SaW RiFlL R^E[L RcGcL RZSRU RiD^E',
	    'PoRJZJ R[QlQ Rc[h[ RVDVU RhNh[ R^TbW RbGcJ R]H^K RjJ[N RZTRV RiDZF',
	    'Po[HlH RRJZJ R]QkQ R]UkU Rg[k[ RVDVT R]L][ RdCd[ R]LkLk[ RgDkE R[RRV',
	    'PoRJZJ R]LjL R]UkU R[[l[ RWDWU R]E]L RdPd[ R]EjEjL R[TRV RjO_P',
	    'PoZFlF RRJYJ Rd[l[ RVDVU R_M_[ R_C_J ReKe[ RfCfJ RlWl[ R_MZR RYTRW RjNhPdR',
	    'PodGjG RRJZJ R[LlL R`UcU RVCVT R^F^L RdCdU R^PZSRV RjOhTdX][',
	    'PoRJZJ RcKlK R\\NaN R\\VaV Re[i[ RVCVV R\\F\\X R\\FaFaV RiCi[ RdOeT RZTRV',
	    'Po^JiJ RRJZJ RWDWU R]M]V R^D^I R^DiDiI R]MjMjV ReWlZ RcV`Y[[ RZTRV RdPcW',
	    'PoRJZJ R^JjJ Rh[l[ RVDVV R]N][ R^D^J R^DjDjJ R]NlNl[ RdPiU RaU_V ReKbT RZTRW',
	    'PoRJZJ RaVhV Rh[l[ RWDWU R\\E\\[ RaPaX RaPhPhV R\\ElEl[ RgHiN RbH_N R[SRV',
	    'PoSH^H R`HlH RVUiU RR[l[ RYCYQ R_Q_[ ReCeQ RZJ]M RXI\\L RgJlO ReFgL RcK_O RWKRP RXFVM ReFbM',
	    'Po`JgJ RRJZJ R`[h[ RVCVU R`U`[ RgXh[ RhUlY RcCiIlJ RcC\\J R`RdT R]MhMfR R\\VZZ RZSRV',
	    'PoRJZJ R\\LjL RcSjS Rc[l[ RVCVU R\\G\\J R\\GkGkJ R^VbZ R^S`X R]TZ[ RcCcG R^P[Y RZSRU RdLcZi[',
	    'PoZHlH RRJYJ R[SbS RVDVV R[L[R R[LbLbR RiDkF RYTRV RcUYW RfDfMhYl[mW RkLgUb[',
	    'PoRJZJ R]JiJ R]RjR RZVlV RVCVT R]F]R RcRc[ R]FiFiJ R]MjMjR RbCbF RZSRU',
	    'Po[ElE RRJYJ RfTjT RY[l[ RVDVU R\\K\\TaTa[ RfTf[ RfEfKjKjT RZTRV RaE`K\\K',
	    'Po]IjI RRJYJ R[NlN RdWgW R_[j[ RVDVV R`R`[ R_RgRgV R]E_G RgOlS RiDgG R`N[S RjXj[ RcH_Q RYURW RdCaN',
	    'Po\\GlG RRJZJ RZTlT RVCVT RcCdG RcRc[ R_LbO RgLkQ RgLcP RhIeO R`I]P RZRRU',
	    'PoRJZJ R]KjK R]OjO R[VlV RVDVU R]G]O RfQf[ R]GjGjO RcCcFcO`R]S RZSRV',
	    'Po\\EkE R\\IjI RRJZJ R[LlL R^SjS R^VjV Rf[j[ RVDVV R^O^[ RcCcL R^OjOj[ RZTRV',
	    'PoRJZJ RgUjU Re[l[ RVDVU R[G[J ReLeZ RdLkLkU R[Q_S R`UZ[ RlWk[ R^J[R RbP^X RcCcG R]NbM`V RZSRV R[GkGlK',
	    'Po]IiI RRJZJ R]MiM R]QiQ RZUlU RVDVT R]D]Q R]DiDiQ RiXl[ RfWiX RZSRV R^YY[',
	    'PoRJYJ R\\TjT RVCVU R\\O\\[ RaOa[ ReOe[ RcDiK RcD[L R^KgJlK RYTRU R\\OjOj[h[',
	    'PoRJZJ R[PlP R]UjU R_[c[ RVDVU RcKc[ R]KjKjV RcFcJ RjFkJ R^E[K RZSRU RjC^E',
	    'PoRJZJ R^RkR R[[l[ RWDWV R\\G\\K RdRd[ R\\GlGlJ RfJkP RbJ]O RcCeG RZURW',
	    'PoRJZJ R`VgV Rg[l[ RWDWU R\\L\\[ R`P`X RdCdL R`PgPgV R\\LlLl[ R\\E`I RlDgI RZTRV',
	    'Po[FlF RRKZK R\\NkN R\\SkS RVCVU R\\J\\S RcCc[ R\\JkJkS RcRjY RcR^XXZ RZTRV',
	    'PoRJZJ R[JlJ R\\OjO RVDVU R\\FjFjO ReTjY R\\Q_T RjQfT RcNfV R`VYY RZTRV RcCc[_[',
	    'PoRJZJ RVCVV R[Q^T R\\F^H RiFgI RjQfU RcIhLkN RcRdVgYl[ RaL[N RbV_YZ[ RZTRV RcObW RcCbK',
	    'Po[GlG RRJYJ R]QiQ R]ViV Rc[k[ RVCVV R]L]W RdJd[ R\\MjMiV RkXk[ RfHlN RaH[N RbC_J RZTRV',
	    'PoRJZJ R[QkQ RVCVT RcMc[ RdRkY RaRZY RbGbK RhFkL RZRRV R]E[M RiD]E',
	    'Po_HcH RRJYJ RYNdN RVDVT RgTa[ RaRcV RfQjZ RjMeX R[QYW RYSRU ReLiX RkHgT R_D^Z[Z RdFgU RcFkFhS',
	    'Po\\FkF R]JjJ RRJZJ R\\MlM R_RhR R\\VjV RVDVU RdOd[ RfMkS RaM[S RcG_P RdCaM RZTRV',
	    'PoRK[K RfMjM R^TdT RfTlT R^ZlZ RWCWU R^O^[ RfOlOl[ RbC^L RdN^O R[TRW RbGlFjM',
	    'Po[FlF RRJYJ R`NjN R`RjR R`VjV R\\ZlZ RVCVU R\\K\\[ R`J`V R`JjJjV RZTRV RdCdJ',
	    'Po\\JlJ RRJZJ R[OlO R[UlU R[[l[ RVDVV R^J^U R\\FcEd[ RiJiU RZTRW RjD`E',
	    'PoSG^G RRK_K RTT^T RYCYK RYQYY RlXl[ RbPfU RVKTP R_JiJiUj[ R[K[P^O^P RdCcMbV RaW]ZRZ',
	    'PoSF]F RRK^K RRP^P RSU]U RXCXK RXPX[ RULUP R[L[O RcCcPbS`W][ RaOeT R`IiIiVk[lW',
	    'Po]IkI RRJZJ R]MkM Rf[j[ RVDVT R]D]M R]DkDkM R`OZT RbS[Y RfVa[ RhQdY R[QRU R^RlQk[',
	    'Po[GlG RRJZJ RZNlN R]ZjZ RWCWU R]R][ RdCdG R]RjRj[ R^H`L RhHgM R[SRV',
	    'PoSGkG RYJfJ RYNfN RRQlQ RXVfV RT[j[ R_S_[ RYCYQTW RfCfQhUmW',
	    'Po[FlF RRJZJ R]NiN R[VlV R_[c[ RVCVU R]I]N RcCcG RcTc[ R]IiIiN R[SRU R]QiQcT',
	    'Po\\FlF RRJZJ RZNlN R]WdW Rd[i[ RVCVT R]R]X R]RdRdW RiNi[ RcHkL RZSRU RdDdFaJ\\L',
	    'Po[GlG RRJZJ R]KiK RVCVU R[N[Q R]R]Y RcNc[ R]DiDiK R[NkNlQ RZSRU R]RjRiYfY',
	    'PoRElE R_HjH RUH]H RUL]L RUViV RR[k[ RUEUP R_S_[ RbJfOlQ RiJeN_Q R]E]PRQ',
	    'Po\\IkI RUViV RR[l[ R_S_[ R\\DkDkI R`K`QkPkR RXLSR RTDXE RjL_N RRIWI R\\D\\K[Q',
	    'Po\\JlJ RRJZJ R_RlR R_ZlZ RVDVT R_T_[ R_M_R ReKeZ RlMlR R\\DlDlJ RlTl[ RZSRU R\\D\\Q[X',
	    'Po\\IjI RRKZK R\\NjN R[RlR RVCVU R\\D\\N RcDc[ R\\DjDjN RcRjY RcQ]XYY RZTRV',
	    'PoWQgQ RUVjV RR[l[ RRHRM RWLWQ R_C_H R_Q_[ RWLgLgQ RRHlHlM RVDXG RhDeG',
	    'PoSG^G R`GlG RYR\\R RfRiR RUVjV RS[l[ R_S_[ RfDfG RTQRR RaQ_Q RYDXGVP RdGbO RdKjKjR RWK]K]Q',
	    'PoRJZJ R]KbK ReKjK R]QjQ Re[l[ RWCWU R]E]Q RfQfZ RdEjEjQ RlXl[ R_V\\ZY[ RbD\\E R`Q^X R[SRU',
	    'PoSD^D RSM^M RSR^R RUViV RR[l[ RSDSQ RYDYI RSI^I^M R_S_[ RYMYR RbElEhLdOaQ RbFfMjQ',
	    'Po^IkI RRJZJ R^NkN R^TkT R^YlY RWDWT R_G_[ ReGeY R^IZM R`C\\L RfCdI R[SRU',
	    'PoRFlF RYJeJ RUPhP RTTjT RUWiW RR[l[ RYCYJ R_J_[ ReCeJ RULhLhP RULVP',
	    'PoRJYJ R^LjL R_VhV Rh[l[ RVDVU R[D[[ R_O_X RdLdV RhOhW R[DlDl[ R`GaJ RgFfJ RZSRU',
	    'PoZFlF RRJZJ R[LjL R]SiS R]WiW Re[i[ RWDWU R]O][ RcCcG R]OiOi[ RfHkM R`F^L R[SRU',
	    'PoSEkE RUI\\I RbMiM RSRlR RUViV RR[l[ R_S_[ RbIiIiM RUIUM\\M\\R RbEbI R\\E\\I RbMbR',
	    'PoRJYJ R\\KbK RfKlK R\\RbR RfQlQ R^[b[ Rh[l[ RVDVV R\\DbDbZ RfDlDl[ RYTRV RfDfTd[ R\\D\\R[X',
	    'PoRJZJ R^KjK R`VhV R\\ZlZ RVDVU R\\E\\[ R`P`V RdGdP R`PhPhV R\\ElEl[ RZTRV',
	    'PoRJZJ R\\RjR RVDVU R]J]R R[KjKjR ReRe[lZl[ RgTiW R_G[L R`C^I RaU^YY[ RZSRU R_FgFeIdKaV',
	    'Po]GjG RRJZJ R\\KlK RVDVU RdCdK RbO\\U RfMkO R_SdXl[ R_N[P R`RiRdW\\[ RZTRV',
	    'Po^GlG RSG\\G ReQiQ RUVjV RR[l[ RXCXR R_S_[ RWG\\L RdCeG RaN]Q RVKRO RXFUM RbJjJiQ RbGaO',
	    'Po\\ElE R_JkJ RRJZJ R^OlO R^TkT R\\ZlZ RVCVV RdGdO RdQdZ RZTRV R\\E\\Q[Y',
	    'PoRF_F RbKlK RSK_K RUViV RR[l[ RZHZS R_T_[ RhJhS RWCTK RbEbLaR RjDbE RYORP R\\OTP R_OWP',
	    'Po\\IjI RRJZJ R\\MjM R\\SbS Re[l[ RVDVV R\\D\\M R\\P\\[ RePe[ R\\DjDjM RlXk[ RkRdU RZTRW RbY\\Z',
	    'Po^IkI RRJZJ R]LlL R_PkP R^UlU RVDVU R^E^I RdCdI RePe[ RlElI RZSRV R]L]S\\Y',
	    'PoRJYJ R[QkQ RVDVU R\\E\\H RcNc[ R\\EkEkH RdRkY RfHjM RbR\\X RaH\\M RYTRV',
	    'Po[FlF R_MiM R_PiP RfTiT RUWiW RR[l[ RTETT R_U_[ R`H`S R_JiJiS R`H[M RbC_K RTEZEXJZMYQVQ',
	    'PoRJZJ R]ZkZ RVDVU R]P][ R]PkPk[ ReUgX R\\H_L RbHfL RgHlM RkDgH RdNbP R]E\\H ReDbG R`SeU RgQcV_X RZTRW',
	    'PoRKZK RfMkM R[RlR RVDVU R[F[J RcCcF RfFfM R[FlFkI RfOjP RcQeWgYl[ R_L\\N RZTRV RbV_YZ[ RaF`L RcNbW',
	    'Po\\IkI RRKZK R[NlN R]UiU R[[k[ RVDVV RcQc[ RfNkS R]D_G RaN[S RcH_P RhDgH RZURW RcD`N',
	    'PoZElE RRJYJ R[PkP R[UkU RZ[l[ RVDVV R[I[P R`E`P RcQc[ RfEfP R[IkIkP RYTRX',
	    'Po]IkI RRJZJ R]PjP R]TjT R[WlW RVDVU R\\E\\H R]L]S RdGd[ R]LjLjS R\\DkEkH RZTRV',
	    'Po[ElE RRJZJ R^MjM R\\UkU R\\ZkZ RWDWU R\\Q\\[ R^H^M RdQdZ R^HjHjM R\\QkQk[ RZSRV',
	    'Po\\FlF RRKZK R]RjR RWCWU R]J]R R`L`P RcCc[ R]JjJjR RdSkY RcQ\\Y RgLfP RZTRV',
	    'PodHiH RRJZJ R`WhW Rg[l[ RVDVU R\\N\\[ R_D_N R`Q`X RdHdN R`QhQhW R_DjDjN R\\NlNl[ RZSRV',
	    'Po[GlG RRJZJ RbLhL R]OkO R[SlS RVDVV R]D]O RbDbL RcOc[ RhDhL RcShXlY RcS^XYZ RZTRW',
	    'Po^JgJ RRJYJ RZMlM RVCVU RgTlY RkOhR RdMhV RbN[Q RbR[U R`OdSaW[Z R_C^J R_FhFfM RZSRU ReVd[`[ RdReX',
	    'Po_JlJ RRJYJ RaOkO R^TlT RVDVU R]G][ RaEiEhJ RgWk[ RdV`[ R\\IXM ReNeThY R^C[L RbL_R RYTRV RePdW',
	    'Po\\IiI RYLkL RUViV RR[l[ RWGWS R\\D\\I R_S_[ RbIbS R\\DiDiI RWGRL RcLfPlR RXCVI RaL]PYR',
	    'PoRJYJ RbLjL R`PlP RVDVT R^H^[ RbEbL RfLf[ RbEjEjL RhUlY RcU_Y RfOiV RfObW R_DZO RZSRU R^F]M',
	    'Po^IkI RRKZK R^MkM RaViV Rh[l[ RWCWU R]Q][ R^D^M RdDdV R^DkDkM R]QlQl[ RhSiX RZTRV',
	    'Po]HiH RRJZJ R]LiL R[OlO RcTjT RVDVU R]D]L RdOdZ R]DiDiL R]TZZ RZTRV R]U^XaZg[l[',
	    'Po`HkH RVSYS RUViV RR[l[ RYKYS R_S_[ RTE]EYISQ RVHZI RaGbLfOkQ RRK^K\\N RbC_J RhGfLcO]R',
	    'Po]HcH RRJZJ R]VjV R]ZjZ RVDVT R]C]N R]Q][ RbObQ R]QjQj[ ReCeNlMlN RkFeH RZRRU RcMZN',
	    'Po[EkE RRJZJ R[TkT RVDVT R\\I\\Q RaIaP ReIeP RcSeWgYl[ RcEaJ RZRRU RbV_Y[[ RcQaX R\\IjIjPhP',
	    'Po]HjH RRJZJ R]LjL R^PlP R^XiX Rf[j[ RWDWV R]D]L R^T^X R]DjDjL R`N[S RdR`V RcThV RZURW RlQk[',
	    'Po`EkE RRJXJ RdKjK R_ZlZ RVDVV R]C][ R^I_M RdSfU ReNgP RXURV RZIYO ReEaU RjLhZ',
	    'Po[FlF RRJYJ R^JhJ R^MhM RZQlQ RfVkV R\\ZlZ RVCVU R\\Q\\[ R^C^Q RfQfV RhChQ R_V]W RaQ`V RZSRU',
	    'PoRJZJ R]KjK RVCVU R]E]K RjEjK RfMhNkQ RdGgI RaP[U R_M\\Q R_SbWk[ R`EdF RfDbI_J RZTRW R_SiSdW`ZZ[',
	    'PoRJXJ R^JgJ R[QaQ R[UaU RUCUV R[M[[ ReNeW RcDfGlJ RcC_HZJ RXURV R[MaMa[_[ RjMj[g[',
	    'Po]JjJ RRJZJ R]NjN R[RlR R\\VkV RY[l[ RVDVU R]F]N RcRc[ R]FjFjN RcCbF RZTRV',
	    'Po]JbJ ReJkJ RRJZJ R^MjM R\\QlQ Rc[h[ RVDVV R]D]J R]DbDbJ ReDeJ ReDkDkJ R`Q_UjUh[ RZTRW',
	    'PoVFiF RSIkI RTM]M R`MjM R_QkQ RWQ]Q RSQZQ RRTlT Rc[k[ RYKYQ R_C_I RcTc[ ReKeQ RlXk[ R[TYXWYS[',
	    'Po\\DlD RRJYJ R`JjJ R`MjM R^QlQ R\\YlY RVDVU R\\D\\[ R`G`M R`GjGjM RbTjX RcN`T RZTRV RiReV^W',
	    'PoSF]F RfKjK RRK^K RRP^P RRU]U RXCXK RXPX[ R`E`[ R`EjEjJ RULUO R[LZO RaNkNgVc[ RbNgVk[',
	    'PoSH^H RSK^K RgPkP RSS^S RUViV RR[l[ RSESK RTMTR RSE^E^K R_S_[ RbEbS RbEkEkP R\\MSO',
	    'Po^JlJ RRKZK RaQkQ RaUkU RaZkZ RWCWU RaMa[ ReDeJ RaMkMk[ R^DlDlJ R[TRV R^D^Q\\Y',
	    'Po^HjH RRIZI R^LjL RZOlO RVCVT R^D^L R^DjDjL RbR[Z R`OYU RZRRU R]RkRj[g[ RgReWa[',
	    'Po\\GgG RRKZKlL R_UjU R_ZjZ RVDVV R_Q_[ RbCbL R_QjQj[ ReNhO RjFcNZR RZTRW',
	    'Po\\FlF R_JkJ RRKZK R^MlM R_SjS R_WjW Rf[j[ RWCWU R_P_[ R_PjPj[ R_IZN RaC]L ReJeM RZTRV',
	    'Po]GkG RRJZJ R\\MlM R^UjU R^ZjZ RVDVU R^Q^[ RcCeG R^QjQj[ R`H`L RhHfM RZTRV',
	    'Po^HgH RRJYJ R^SgS RaYgY R]YcY RVDVU R^L^S RbHbY R^LgLgS RfVh[ R[EjEkXl[ RYTRW RiTj[ R[E[SY[',
	    'Po[HlH RRJZJ R\\PjP R\\TjT R[WkW RZ[l[ RVDVU R\\L\\T R[EcEc[ R\\LjLjT RZTRV RiCaE',
	    'PoRIZI R\\IjI R\\NjN RVCVS R\\D\\N RcDcN RgQg[ R\\DjDjN R_W[[ ReNiSlT RZQRU RaN^RYT R_R^X',
	    'PoYHkH R[LiL RZNjN RXQkQ RUWiW RR[l[ R_T_[ RbCbS R[EiEiK RXMSR RTDXE RRHWI',
	    'PoTF^F R`HlH RRJ`J RTN_N RYR_R RVR]R RcShS RUViV RR[l[ RYCYJ R_S_[ RhChS RbKeN RYKYRRS',
	    'PoRJZJ R\\JkJ R[NlN RcSkS R\\ZlZ RVDVU R\\D\\J R\\R\\[ RaDaJ RcNcZ RfDfJ R\\DkDkJ RZSRV',
	    'Po\\FkF R\\JjJ RRJYJ R[NlN R_UhU R_ZhZ R_Q_[ R]QhQh[ RfNlT R`N[S RbH^Q RVCWURV RcC`N',
	    'Po[HlH RRJYJ R^LdL R^VcV RVDVU R^O^X R^OcOcV RhDkF RgUa[ RjMfW RYTRV RfCfMfOhZ R[H[RZY RlWk[i[',
	    'Po\\IkI RRJZJ R^OlO R]TlT RVCVU RhOh[ R\\DkDkI RaKbN RhKgN RZTRV R\\D\\RZY RaOaT`Z][',
	    'PoUIiI RUMiM RUViV RR[l[ RUDUM R_D_M R_S_[ RUDiDiM RaRlT RjNbP R\\RRT RTN[O',
	    'Po[GkG RRJYKlL R^UhU R^[j[ RVDVV R^Q^[ R^QhQhU RgCfMkR RjXj[ R_C`MZS RZTRV',
	    'Po[FlF RRJYJ R\\KlK R^PjP Rh[l[ RVDVU R`C`I RcRcZ RhChI RiRi[ RdIdK RYTRV RlXl[ R]K^P R^R]Y',
	    'PoRJZJ R\\KkK R[OlO Rd[h[ RVCVT R\\E\\J RaEaJ RfEfJ R\\EkEkJ RcLdO R`V[[ RaS^Y R[SRU RaSiSh[ RaO`W',
	    'PoRJYJ R\\JaJ RiLlL R\\PaP RVCVV R\\E\\[ RdDjDjL ReUk[ RdQgX RcPkPhVeYa[ RaD\\E RYTRV RaUYV RdFcM',
	    'Po[FlF RRJYJ R]SjS Rd[i[ RVDVU RaFaM RfFfM R[OjOjS RhHlL R_H[L RcCdF R]S\\VkVi[ RZSRU',
	    'PoRJZJ R^KiK R^SiS R^WiW R[[l[ RVDVU R\\G\\J R^N^W R^NjNjW R\\GkGkJ RcCcG RZTRV',
	    'Po`EkE RTI]I R`KlK RTM]M RUViV RR[l[ RTETR RTE]E]M R_S_[ RaF`K RgLgRlQlR RZN]R RdN`R RfEdN R\\QRR',
	    'Po[F`F RfFjF R[I_I RfIkI RRJYJ R^RhR R^VhV RVCVU R^N^[ RaI\\N RcDeJhMlN RcD_L RZSRV R^NhNh[d[',
	    'PoRJZJ R\\KkK R\\PkP ReVkV RVCVT R\\F\\P R\\FkFkP RcCbF RjTkW RgRfV RdFdZlZlY RZSRU R_P^W[Z',
	    'PoUTiT RR[l[ RSMSQ R_O_[ RSMkMkQ RXH]J RkEhG RUESG R]E[G RbE`G ReHkK RfDdHcI_K RWIRK RYCXI',
	    'Po]HkH RRJZJ R]LkL R^[b[ Rh[l[ RVDVT R]D]K R[NbNb[ R]DkDkK ReNlNl[ R_P\\S R`T\\X RiPfS RjTfX RYSRV',
	    'Po^IjI R\\NlN R^UjU R\\ZlZ RV[Z[ RTEZEZ[ RdPdZ RVIYL RVOYR RhOlS RhDlH R`O\\S R_E\\H ReIiQ RcI_Q RcD`O RTETSS[',
	    'PoRJYJ R_ZiZ RVCVU R\\F\\J R_S_[ R_SiSi[ R\\FkFkJ RdLiRlS RfJjN RcCdF RaJ]N RdL^R[S RYTRV',
	    'Po\\IjI RRJZJ R[MkM R]UjU RZ[l[ RVDVU R\\D\\I R]P]U RcCcI R]PjPjU RjDjI R_V`Z RgVfZ RZTRV',
	    'Po\\EkE R]IjI RRJZJ R[LlL R[TkT RVCVT RcTc[ RhWkZ RhRlU RcCcL_P RdPaT RfNbR R_V[Z R\\ObR RZSRU',
	    'PoRH_H RbIjI RbNjN RTN^N RgSjS RUViV RR[l[ RTJTP R^J^O R_T_[ RbEjEkS R\\C[F RYHYPUT RUDVF RbEbLaQ',
	    'PobGkG RRJYJ RbMlM R[M`M RbQlQ R[W`W Re[i[ RVDVU R[E[X R[E`E`V RgCgL RiNi[ RdTeW RYTRV',
	    'PoRJYJ R]MiM R]PiP R[TlT RVDVV R[E[H R]I]P R]IiIiP R[ElElH RhXl[ R]YZ[ R_W\\Z RcRcT RYURV',
	    'PoZGlG RRJZJ R_OgO R]ZiZ RVCVT R]S][ R_C_I RgCgI R]SiSi[ RcIgNlP RbI]OYP RZSRU',
	    'PoRJYJ R[LiL RUCUT RfJjM RbEfDjH R^PZT RaS[Y RhPcT RjSdY R_D[I R`G^L RYRRU RZPaPa[][ RcPjPj[g[',
	    'Po[IlI RRJYJ RVDVU RcDc[ RcQiWlY RZTRV RcQ]WYY R[L_K_QZP RkLgLgQkQkR RhC]E',
	    'Po]IgI RZMjM R^SaS RUWiW RR[l[ R_U_[ RbIbS RRIVJ RTDXF RfOjS R]OYS RbCeFlI RVNTS RaC^GXI',
	    'Po[FlF RRJYJ R]NlN R^RjR R^ZjZ RVDVU R^U^[ RdCdF RdHdR R_JjJjR R^UjUj[ RZTRV R[F[RZY',
	    'Po[FlF RRJZJ R^NiN R_XhX Rh[k[ RVDVU R\\P\\[ R^I^M R_S_Y RcCdF R_ShShX R^IiIiM R\\PlPl[ RZSRV',
	    'Po]IiI RRJZJ RVDVT R[E[H R[EkEkH RjNfQ RcIaKZN RZSRV RdR_WYY R`LcPdRdZ`[ RdPlW RcO`Q[S',
	    'Po[ElE RRJZJ R\\OjO R[SlS RVCVT R\\I\\N RaEaN RcPc[ ReEeN R\\IjIjN RcShXlY RYSRU RcR^XYY',
	    'Po]HhH RRJYJ R[KkK RVCVT R[E[I ReSeZ R[EkEkI RjNgPkX RdK[O RdSbUZY RhPcQZTRU RdPeW Rd[_[',
	    'Po]FlF RRJYJ R\\PlP Rd[h[ RVDVU R\\K\\O RdCdF RdMeP R\\KlKlO RaW][ R`G`J RiGgK RbQ`X RZTRV RaUjUh[',
	    'PoUKhK RVNhN RRRlR RXWeW RT[k[ RSFSI R_C_F R_T_[ RSFkFkI RdHdRgUlW RZHZRWVRW',
	    'PoRFlF RSJkJ RUWiW RR[l[ RZCZH R_S_[ RdCdH RbKaSkRkS RUOZP RZJWNSO RWM^M[QTT RiLaO',
	    'PoRJYJ RbLjL RbWkW Rb[l[ RVDVU RbGbW RbGjGjL RbQkQkW R[E^H R[M^M^XZ[ R^Xc[ RfCeG RYTRV',
	    'Po^IkI RRJZJ R`NiN R`QiQ R`TiT R\\VlV RVDVU R\\L\\V R^C^H R`K`T R`KiKiT RfXl[ R`XZ[ RZTRV RiD^F',
	    'PoRJZJ R]LiL R]OlO Re[j[ RVDVU R`U`Z R]FiFiL RfUhW RcUdY RbCbG R\\UZ[ RZSRU R]F]SkSj[',
	    'Po\\IjI RZLkL R[OjO RYSlS RUWiW RR[l[ R\\D\\I R_T_[ RcLcR R\\DjDjI RXNRS RTDXE RRIVJ',
	    'Po]IjI RRJZJ R]OjO R]SjS R]WjW RVDVU R]L]V R]D]I R]DjDjI R]LjLjV RfXl[ R`XZ[ R[SRU',
	    'Po\\ElE RRJZJ R^LjL R_UiU Rh[k[ RWDWU R\\O\\[ R^H^L RdUd[ R^HjHjL R\\OkOl[ R`QbS RgPeT RZTRV',
	    'Po\\FkF R^KjK R^NjN R[QlQ RUWjW RR[l[ RSDST R^H^N R_T_[ RdDdT R^HjHjN RXKZO RSEZEWJ RYQWQ',
	    'Po^JgJ RRKYK R`NgN RY[l[ RVDVT R\\T\\[ R`T`[ ReTe[ R\\TiTi[ RgOlT RaEfEkJ R^PZT R^FZJ RYRRU',
	    'Po[GkG RRJYJ RZLlL R^[a[ RVCVS RaPa[ RgCgMlS RfLhP R_C_NYS RdSeX RiTkX R]S[X RYQRT',
	    'Po]GlG RRJZJ R^PiP RX[l[ RVDVU R\\S\\[ R^J^P RaSa[ RfSf[ R^JiJiP R\\SjSj[ R_C[L RZTRW',
	    'Po\\FbF RRJZJ R\\TcT RfTlT R\\ZlZ RVDVV R]O][ ReNlNl[ R]H^L RkGiL RcGdK RcN\\O RZTRV RkD`F',
	    'Po[FlF RRJZJ R]MiM R]PiP R]SiS RZVlV RVDVU R]I]S RcCcI R]IiIiS RfXl[ RZSRV R`XY[',
	    'Po]ElE RRJZJ R_MkM R_QkQ RVDVU R_I_Q R_IkIkQ RjUkY RdFdI R`T^Y RZTRV ReQe[b[ R]E]Q[Y',
	    'Po]IiI RRJZJ R]NiN RY[l[ RVDVU R\\R\\[ R]E]N RaRa[ ReRe[ R]EiEiN R\\RjRj[ RZSRW',
	    'PodJlJ RRJYJ R\\JbJ R\\ObO Rf[j[ R^[b[ RVDVV R\\F\\R R^C^G R\\FbFb[ RjCj[ RdNgS RYRbR]WXY RYURW',
	    'PoRJZJ RhNkN R]UkU R]ZkZ RWDWV R\\E\\N R]Q][ RdQdZ R]QkQk[ RdElElM RaHbK\\N RZTRV RbD\\E RgEfL',
	    'Po\\FkF RXMlM R[SlS RcUgU RUWjW RR[l[ R_U_[ R^EYI RTDXE R`OdP RRIVJ RVNST RaKeK R\\I[S R]IiIhT',
	    'PobIkI RSI]I RSM]M RaMkM RgRkR RUVjV RR[l[ RSFSR RSF]F]L R_S_[ RbEkEkR R[N]Q RWDYF R\\PRR RbEaL`Q',
	    'Po^FlF RRIZI R^KjK R^NjN RZQlQ Rf[j[ RVCVT R^H^N R^HjHjN R`QZW RbU\\Z RfTcZ R_C]H RZSRU R]TkTj[',
	    'PoRKYK R`PkP RaSjS R_VkV Ra[l[ RVDVW RfNfY RYN]N]XY[ RdE`I R[E\\I RbGfKlM R]Xb[ RcFjFfK_M RYURW',
	    'Po\\HlH RRJYJ R\\ObO RfOlO R\\TbT ReTlT Ri[l[ RVCVV RdCdH RfKlKl[ R\\E\\I RZTRV R\\KbKbZ_Z RkEkI R\\K\\T[Z RfKfTdZ',
	    'PoTFlF RTMjM RYP`P RXWiW RU[l[ R\\F\\M R`C`F RYMYT`T`[ RTIjIjM RcFcMcTkSkT RjObP RTFTSS[ RcIbP R]SWT',
	    'Po\\FlF RRJZJ R\\OkO R_TcT Rf[l[ RVDVU R_Q_[ RaFaO RfQf[ RfFfO R\\JkJkO RlXl[ RcCdF RkSfU RZTRV RdZ^Z R\\F\\SZ[',
	    'PocGlG RRKZK R\\YkY RVCVU R\\K\\[ RcCcK R\\KkKk[ R`OhW RdVeX R_R`T RbMcO RgQiS ReQ_X RfMdT RZTRV',
	    'Po[DlD RRJYJ R`MhM R^VcV RfVkV R[ZlZ RVDVU R[D[[ R^O^V R`G`L R^OcOcV RfOfV R`GhGhL RfOkOkV RYSRU',
	    'PoSF_F RbJlJ RSK^K RSN^N RRQ_Q RUWiW RR[l[ RSHSN RYCYU RSH^H^N R_U_[ RhJhT RkDbEbMaR',
	    'PoTFkF RaKkK RXViV RU[l[ R[H[Q R`R`Z R`CaF RfHfQ RfKkP RfK`P RZMWP RWK]K_M R[IYO RTFTSS[',
	    'PoeFlF R[FbF RRJYJ R]MjM R]QjQ RZUlU RVDVU R]J]Q R`C`H RhChH R]JjJjQ RdWkZ RYTRU RcRaW_YZ[',
	    'Po[FlF RRJZJ R\\KjK R\\NjN R[UlU Rc[g[ RVDVT R\\H\\N RcCcQ RgSg[ R\\HjHjN R^W`Y R[RiQlR RZSRU',
	    'PoZGkG RRJYJ RVDVT R\\P_T R]J_M RgPjT RfJjL R`P[U RjIfM RjPfT R`I[N RcCcQeVhYl[ RYSRV RcP`V]YY[ RcMbU',
	    'PoRF^F RTK]K RRQ_Q RUUXU RUWiW RR[l[ RTHTK RXCXF RXOXT RTH]H\\K R_U_[ RTN]MXO RdDdLaR RaHiHiOkTmR RbLeO',
	    'PoZHlH RRJYJ R]KjK R\\QjQ R[TlT RVCVT R\\N\\Q R]E]K RcCcQ R]EjEjK R\\NjNjQ RYSRV RaQ^Wk[ RhUdYZ[',
	    'Po\\IkI RRJYJ R^LbL RgLkL R^ObO RgOkO R_TkT R]WlW RVCVT RdJdP RdQd[ R\\DkDkI R_Q^V RZSRU R\\D\\QZY',
	    'Po_GgG RRJYJ R_JgJ R\\QkQ RVCVT R\\M\\Q R]D]K RaMaQ RfMfQ R]DiDiK R\\MkMkQ RYSRU R^U`WdYl[ R[TiTfWbYZ[',
	    'Po[DlD RRJZJ R\\MkM R]QjQ R[TlT R`[c[ RVDVU R\\H\\M RaDaM RdTd[ RfDfM R\\HkHkM RhVlZ R^VZZ RZTRV',
	    'Po\\FkF R]QjQ R]UjU Rf[l[ RVCVU R]M]T RcCcF RfUf[ R]MjMjT RlXk[ R_G`J RhGgJ RZSRV RSJYJ R\\KlK RaU^YY[',
	    'Po[HlH RRJYJ R`LdL R\\PeP R^YaY RVDVT RaJaY RcSdV RhDkF RgUa[ RjMeW R]S]W RYSRU RfCfNhY R[H[SYZ RlWk[i[',
	    'PoSH^H RSL^L RSO^O RWS_S RSS[S RbTeT RUWiW RR[l[ RSESL RYEYS RSE^E^L R_U_[ RfKfT RcGgI RaEjEfI R`KlKjO',
	    'Po[GlG RRJZJ RbKfK RgYiY RVCVU R\\N\\Q R^R^Y RbCbK RdOd[ RfCfK R^RjRjY R\\NkNkQ RiCiKlK RZTRV R_D^K',
	    'Po\\EkE RRJYJ R[JkJ R]PjP R]SjS R[WlW RVCVU R]M]S RcCcF RcSc[ R]MjMjS R_G`J RgGfJ RZTRV',
	    'PoZFdF R\\JbJ RRJYJ R\\MbM RYQeQ RaUcU R[YcY RVDVU R[Q[[ ReJlJlZ RYTRV Rk[h[ RbCaQaU R\\C]Q\\V RhChOfY',
	    'Po[FlF RRJYJ R]LlL R^OjO R^UjU R^XjX RVDVU R^R^[ RcCcG RdGd[ R^IjIjO RYSRV R^RjRj[g[ R[F[RZZ',
	    'PoSF]F RRI_I RRM_M RSP]P RUViV RR[l[ RXCXI RXMXT R_T_[ R`GiGiR RdCcMfP RbN^S RUJUM R_LeN R[J[M RlPkSlS RdGcO',
	    'Po[HjH RRJZJ ReMkM RZN`N RVCVR RiKlM RfEjH RcJhPlQ RhIfN R\\I[N R`C_H RYQRT RcJ]PZR RcP]S RfS]W RiTgWcY\\[',
	    'Po[GkG RRJYJ R[LlL R`SgS RVCVT R`I`S RcCcG RgIgS ReVg[ RiVl[ R^V\\[ RYSRU RaVb[ R[G[QYY',
	    'PoaEkE RSH]H R_KlK RSK]K RSS]S RUWiW RR[l[ RSESK RTMTS RSE]E]K R_T_[ RaEaK RfKfSlRlS ReM`R RgHdP RgEeP R\\NSO',
	    'PoZFdF RRJYJ R[McM RZPcP RVDVU R[I[M R[IcIcM RfEf[ R^D_F RjNlT RYTRW RfElEiM RbQ_S_[[[ RkWhW RcTYV',
	    'PoTIYI RRMYM R\\MlM RdTgT RUWiW RR[l[ RTDTI RYCYT R_T_[ RgKgT R_ObR R]GbI RaFjEgHaK RcD\\G R`FfI\\L RUMTS',
	    'Po[FlF R_JgJ RRJZJ R\\QjQ R\\TjT R\\XjX RZ[l[ RVDVV R\\M\\Q R_C_J RcJc[ RgCgJ R\\MjMjQ RZTRW',
	    'Po[FlF RRJZJ R^OiO R_WgW Rg[k[ RVCVU R\\K\\[ R_S_Y RcKcR R_SgSgW R\\KkKk[ R`G`K RcCcF RgFfK RZTRV',
	    'Po[FlF RRJZJ R`WgW Rh[k[ RVCVU R\\L\\[ R`R`Y R`RgRgW R\\LkLk[ ReNhQ RbDdF RbN^R R_H`K RgHfL RZSRV',
	    'PoSFkF RVLhL RVOhO RRSlS RXWfW RT[k[ RVIVO RZCZH R_T_[ RdCdH RVIhIhO ReSgUlW R]OWVRX',
	    'Po]IkI RRJZJ R]NkN R]RkR R]VkV R]ZlZ RVDVU R]D]I R^M^[ RdCdI ReLeZ RkDkI R^MZQ R_J^M RZSRW',
	    'Po\\EkE RdIjI RRJYJ R[LlL R^QkQ Rf[k[ RVDVU R^H^L RaUaY RdEdL RhUiX RdUeX R\\U[Z RZSRU R^N]TlTk[',
	    'Po_HgH RRJYJ R]OiO R\\VjV R\\ZjZ RVDVT R\\R\\[ R]K]O RcRcZ R]KiKiO R\\RjRj[ RdDlH RZSRU RbC`FZH RcDdD',
	    'PoRJZJ R\\KlK R\\OlO R^VkV R^ZkZ RWDWU R\\G\\O R^R^[ RdGdO R^RkRk[ R\\GlGlO R_C`F RiChG R[SRV',
	    'PoZFcF ReHlH RRJYJ R[NbN R[RbR RYVcV Rd[l[ RVCVU R[J[R R^C^J R^R^[ R[JbJbR RlXl[ RdMjMdZ RfCdL RYTRV',
	    'Po[FkF RRJYJ RZLlL R_WgW R\\ZjZ RVDVU R\\P\\[ R_S_V RcCcL R_SgSgV R\\PjPj[ R]H`J RiHfK RYTRU',
	    'PoRJYJ RZRlR RVDVU RcPc[ RaEiEiM RcRhXlY R[H]J R\\D^F R^L[O RkGkL RaG`K RbN`O RYTRU RcR]XZY RgOdO RdEcM',
	    'Po[JcJ RdJlJ RRJXJ R]NcN R\\RdR Rf[i[ R][`[ RVDVU R`R`[ R[DcDcJ RjCj[ RcTdY ReOgS R]T[Y RXTRV R[D[RYZ',
	    'Po[GlG RUViV RR[l[ RSDST R_T_[ RgMlR R_D`G RhDfF RdGhO RSEZEWJ RdG[K RbK\\N RkJfK RcO[R RXKZOYQWR RcKdPcS`S',
	    'Po\\FjF R[JlJ RRJYJ R[MbM RdMkM RdQlQ RZTlT Re[l[ RVDVU R_K_Q RcCcJ RfTf[ RgKgQ RlXl[ RYSRV R`T_X]ZY[ RbQ[Q',
	    'Po[OjO R]VhV R]ZhZ RVDVU R\\H\\O R]R][ RcIcO R]RhRh[ RRKZJkIjO RaEfDlJ R_EZJ R_K_N RfKfN RZTRV',
	    'PocEjE RRJYJ RaLhL R^LeL RcOkO R\\[l[ RVCVT R^R^W RbQb[ RcCcH RcJcO RfQf[ R\\HlHkK RZSRU RjRjWfW RbW^W R\\H\\RZY',
	    'Po\\IbI ReIkI RRJZJ R\\PjP R\\SjS RZWlW RVCVT R\\D\\I R\\L\\S R\\DbDbI RcLc[ ReDeI R\\LjLjS ReDkDkI RZSRU',
	    'Po\\GkG R]JjJ RRKYK R\\MkM R[RlR R]ZiZ RVDVU R]U][ RcGcR R]UiUi[ R_C`G RgCfG R_O_R RhNgQ RZTRV',
	    'PoRJYJ R[Q`Q RbTjT RVCVU RZM`L`Q RcLhLhQkPkQ R[G^I ReGjL RjHgI R`GZM RhDeG RdDfI RcUgYk[ R[DaD^I RYTRV RiUfYb[ R_[\\[ R[QZU`U_[ RcLcQ',
	    'Po[IlI RRJYJ R[NkN R\\QjQ RZTlT RVDVT RaDaI R_J_N R\\E^H RbNcUfYl[ RiEhH ReDeIfM RZSRU RbV_YZ[',
	    'Po\\IiI RRKZKlL R]QiQ R]TiT R]WiW RVCVU R\\E\\I R]N]W RcCcL R]NiNiW R\\EiEiI RfYk[ R`YZ[ RZTRV',
	    'Po\\FkF RRJYJ R[KlK R]QjQ R]TjT R\\WkW RZ[l[ RVDVV R]N]T RcNc[ R]NjNjT R_G`J RcCdF RgGfK RZURW',
	    'PoRJYJ R[KkK R]ViV R]ZiZ RVDVU R]R][ RdDdP RcRcZ R]RiRi[ R^G_I RcJjP RcJ\\Q RhFgI RYTRV RiC_E',
	    'Po[HlH RRJYJ R_OhO R`XgX Rg[k[ RVDVT R\\Q\\[ R`I_O R`T`Z R`TgTgX R^LhKhO R\\QkQk[ RfHkM RaH[N R\\EcE_L RZSRV RiDbE',
	    'PoUHiH RUKiK RUNiN RSQkQ RUWiW RR[l[ RUDUK R_D_Q R_U_[ RUDiDiK RaRcT RhRkU RVRSU R[R[U',
	    'PoYGcG RRJYJ R[NbN R[[^[ RVDVU R[J[N R^S^[ R[JbJbN RZQbQ^T RgVb[ RfRjZl[ RiPfX R^C^G RYTRV ReJgW ReJkJhV RfCdO R`UYV',
	    'PoRF^F RTK]K RTN]N RRQ_Q RUUXU RUWiW RR[l[ RTHTK RXCXF RXOXT RTH]H\\K R_U_[ RbHdNhRlS RcD_L R\\NWQ RbHjHfNbR^S',
	    'PoRJZJ ReMjM R]PjP R]RjR R[UlU Rb[f[ RVDVU R]J]R RaGaL R_GgGfM RgSg[ R]JjJjR R^W`Y RbDgDlH R_D[I RZTRV',
	    'Po]HgH R[IlI RUWiW RR[l[ RSDST R_T_[ RhOlS R_C]G RXKZO RSEYEWJ R_EhEfI RdI\\M RcL\\O RjLdN RcKeO\\S RYQVR RePdSaT',
	    'Po\\GlG RRKYK RY[l[ RVDVU R]T][ RaTa[ ReTe[ R]TjTj[ RlOlR RgDkE RiLcR RhQkR RjIgO RYTRV RaQ^Q RdDfO R\\G\\L[Q R\\KbKaQ',
	    'Po\\EkE RaHjH R_KkK R_OjO R_RjR RfUiU RUWiW RR[l[ RTETU R_M_T R_U_[ ReHeK R_MjMjT RWJZM RaE[K RTEZEWJ RZQVQ',
	    'Po[ElE RaHkH R_KlK R_OjO R_RjR RgTjT RUWjW RR[l[ RWCWT R_M_T R_V_[ ReHfK R_MjMjT RaE[K RYFZI RSGRM',
	    'Po[DlD RRJZJ R\\LkL R]QjQ R]TjT RZWlW RVDVU R\\G\\K R]N]T RaDaK RdTd[ RfDfK R]NjNjT R\\GkGkK RZTRW',
	    'PoRJYJ R_LfL R]ThT RZ[l[ RVDVV R]O]T R]OhOhT RfHkN RkHhJ R[G_IZN RhDfG R_V_Z RdDgJ R[EaE^J RZTRV RfVeZ',
	    'PoRJYJ RYMlM RZTlT RVDVU R]F]T R`W`[ RaGaT ReGeT R\\GjGiT RdWf[ RiVk[ R^C[J R\\WZZ RYTRU',
	    'Po\\FkF R[JlJ RRJZJ R]PjP R]TjT R]WjW RVDVT R]M]W R_H_L RcCcH RhHhL R]MjMjW RfXl[ RZRRV R`XZ[',
	    'Po\\FlF RRJYJ R[JlJ R]RjR R]VjV RVDVV R]N]V R`C`J RdJdV RgCgJ R]NjNjV RfXkZ RaX[[ RYTRV',
	    'Po\\EkE RRJYJ R]KaK RfKjK R]NaN RfNjN R]RjR R[UlU R`ZjZ R\\ZfZ RVDVT R[H[L RcEcO R[HlHlK RfWk[ RaU\\Z RYSRU',
	    'Po`FjF R_JkJ RRJYJ R_OkO R`RjR R_VlV Ra[l[ RVDVV ReCeJ ReOeZ R[F]H RhKgO RbKbM RYN]N]Xb[ R]XYZ RYURW',
	    'Po[FlF RRJYJ RZNlN R[SkS RZWlW RVDVU R^J^S R`C`H RcJc[ RgCgH R[JjJiS RYTRV',
	    'PoZFlF RRIYI R[KgK RZOlO R^VjV R^ZjZ RVCVT R_R_[ R_C_H RaHaO RgCgH R]SjSi[ RgQjT RZSRU RjIdOYU',
	    'Po[HbH ReHlH RRJYJ R[LbL ReLlL R`TgT R`XgX Rh[l[ RVCVU R[D[[ R`P`Y R[DbDbL ReDeL R`PgPgX ReDlDl[ RZTRV',
	    'PoRJYJ R^LiL RZUlU RVDVU R\\F\\R RcHcQ R\\FkFkR RcL^Q RbCaF RbMfO RdVfYk[ RfIfK RYTRV RcSaW_YZ[ RfG`H',
	    'Po[FkF RRJYJ RZOlO R`WfW R\\ZjZ RVCVU R\\Q\\[ R`T`W RcCcO R`TfTfW R\\QjQj[ R^J`L RgJkM R^H\\M RhHeM RZSRV',
	    'PoRE^E RhIlI RSJ]J RSL]L RRO^O RSS]S RUWiW RR[l[ RSGSL RTPTS RXCXS R]P]S RSG^G^L R_T_[ RbDhDhI RbLePlS R`KjLfP_S RbDaI',
	    'Po[JbJ RRJXJ R[NbN RZQcQ RUDUV R[F[N R]C]F R[FbFbN RgWc[ RfRjZ RiQeY Ra[][ ReLhX RjLgW RYURV ReJgV ReJkIhV R^O^Q[Y RfCdN R]UbUa[',
	    'Po`HjH R`KjK RUWhW RR[l[ R_U_[ R`D`R R`DjDjK RUHWJ RdKeOhRlS R[KSN R[NRR R\\SXS RkNfO RZG[J]O]R R^FXJRK R[DSF ReQ^R',
	    'Po\\IkI RRJZJ R\\LjL R[PlP R\\TkT R[WlW RVDVV R\\D\\I RaDaI RcIcO RcTc[ RfDfI R\\DkDkI R`QaT RgQfT RZTRV',
	    'PocGlG RRJXJ R\\LaL RcNlN RdTlT R]YaY RVDVU R\\E\\Q][ R\\EaEaK R]QaQaY ReIeL RhNh[ RgDhG RkIhN RXTRW R\\LZW',
	    'Po`FkF RTI]I R_KlK R_NlN RUR]R RUViV RR[l[ RTE]E]I RUM]M]R R_S_[ ReCeF ReKeR RbGbJ RiFhJ RTISR RTETJ RUMUR',
	    'PoTH^H RUL_L RUO_O RUWjW RR[l[ RXJXO R\\J\\O RTD^D^H R_T_[ R\\Q^S RXQUT RbMfQkS RaLjLfP`S RbDhDhJkIlJ RbEaJ RTDTMSS',
	    'Po[DlD R]JiJ RRKZK R]MiM R[PlP R]UjU R]XjX RZ[l[ RVDVV R]R]X R]G]M RcGcM RcRcX R]GiGiM R]RjRjX RZTRV',
	    'PoSHZH RdHlH R\\S_S RUViV RR[l[ RWCWS R_J_R R_T_[ RhChS RVIZM R\\G`I R[EbE_H RhHlP RVHRP RZJcJbM RhGcP RhFjO',
	    'PoRFlF R_JkJ R`MjM R`PjP R`SlS RUWiW RR[l[ R_C_F R_T_[ R`I`S RfIfS RTJWM RXGUK R[JTP RaH]L R\\M[QYSTU RYPRP R[OUP',
	    'PoTHYH RdHiH RULZL RdLiL RTViV RR[l[ RUEUO R_R_[ RdEjEjN R`K\\M R\\JaL R\\EaF[H RRORS RROkOlS RYDTD',
	    'PoZFlF RRJYJ R`NfN R\\PjP R]UiU R]YiY RY[l[ RVDVU R\\I\\P R]R]X R`K`N RcCcF R`KfKfN R]RiRiX R\\IjIjP RZTRV',
	    'PoZFlF RRJYJ R_MfM R\\OjO RZUlU RVDVU R\\H\\O R_J_M RcCcF R\\ScRc[ R_JfJfM R\\HjHjO RcTeWlZ RYTRV RaU_XZZ RhQ`R',
	    'PocEjE RRJYJ RbNjN R^PkP RVCVU RcCcM RhVlZ RhTiX R\\HkHjK RfP^S RcT]V RjReUcW]Z RZTRV ReSfXe[b[ R\\H\\SZY RfJ_K',
	    'Po[FlF RRJYJ R[NkN RYRlR RVCVU R[I[N R_U_Z RaIaN RcCcI RfIfN R[IkIkN RcQeVhYl[ RbSYW RkTeV RZSRU RcNdU ReY[Z',
	    'PoRJZJ R^OiO R\\VkV R\\ZkZ RVDVV R[H[K R\\R\\[ R^K^O RcRcZ RcCcG R^KiKiO R\\RkRk[ R[HlHlK R^D_F RiDhF RZTRV',
	    'PoZFlF RSJYJ R[LkL R\\OjO RZSlS RVCVT R_U_Z R`J`S RcCcF RfJfS RdSeWhYl[ RfHkJ R`H[J RYSRV RdSZW RkUfW ReY[Z',
	    'PoYFbF RdJlJ RYKaK RYNaN RXQbQ RUWiW RR[l[ RYHYN R]C]T R_U_[ RYHbHbN RiJiT RTDWE RRIVJ RWMTT RkDdEdMcS',
	    'PoZGlG RRJYJ R\\PkP R\\RkR RZVlV RVDVU R\\J\\P R`X`[ R\\EdEcU R\\JkJkP RhWl[ RdWe[ R\\XY[ R_L_N RgLfN RZSRU RjDcD',
	    'Po\\DcD RfHlH RRJYJ R\\LbL R\\PcP RZ[l[ RVDVU R\\D\\P R]S][ R`L`Q RaSa[ R\\HbHcL RfSf[ R]SjSj[ RgKjN RZTRV R`D`H RgCdM',
	    'PoSEkE RfTiT RVWiW RR[l[ RVHVT R_E_S R_U_[ RTIjIiT RYQ]R ReJbN RfPbT R\\KYN R\\PXT RbKfM RXK]M RbQgR RdFlJ RYFRJ',
	    'PoYE^E RaEkE RUT]T RUWiW RR[l[ RVOVT RYCYG RUP^O^T R_U_[ RSGSH`G`J R\\M_P RYL]N RVI\\J_L ReNkT RhLbS RVKSM RcHgP RjFfO RYLVORP',
	    'PoZFlF R\\JjJ RRJZJ RZNlN R[RjR RZ[l[ RVDVU R\\U\\[ R`C`H RaUa[ RcHcM ReUe[ RgCgH R\\UjUj[ RgOkS RaN_R RZTRW',
	    'PoUElE RWK_K RaKlK RWO`O RWR`R R\\T_T RUWjW RS[l[ RWMWT R_U_[ RWM`M`T RhPlT RhGjJ RePaT RfGfMcS RfIhR R\\I_I RWGXJ[IWI RWG_G_J[I RUETMST RfIeQ',
	    'Po[ElE R\\HkH RRJYJ R]MiM R\\OjO RZRlR RbUlU R[YaY Re[i[ RVDVU R[T[Z R[TaTaY RcCcH RcMcP RiSi[ R[KkKkM RdWfY RZSRU',
	    'PoZElE RRJYJ R]KiK R]PiP RVCVT RZMZP R]H]K RcCcF R]HiHiK RZMlMlP ReTkY RdPgW RYRRV RcP\\S RbT[W RjSfU R`RcUdW[Z RdXd[`[',
	    'Po]DkD R^JbJ RfJjJ RRJZJ R]MbM RfMjM R[PlP Ri[k[ RVCVU R[G[J R]S][ RaSa[ RdDdN RcPbS RfSf[ R]SkSk[ R[GlGlJ RZSRU',
	    'PoZHlH RRJYJ R\\LjL RZOlO RX[l[ RVCVT R[U[[ R`U`[ RaPaT RcCcO ReUe[ R\\EjEjL R[UjUj[ RePfS RhPlT R\\P[T RYRRU',
	    'PoVHhH RVKhK RSQ]Q RaQkQ RST]T RaTkT RUWjW RR[l[ RSMST RVEVK RXMXT RSM]M]T R_E_K R_U_[ RaMaT RfMfT RVEhEhK RaMkMkT',
	    'Po[FlF R[IlI RRIYI R`LhL R[NlN R^TkT R^WkW RVCVT R^Q^W R`F`L RcCcF RdNdW RhFhL R^QkQkW RgYl[ RYRRU RbY\\[ R[F[QZX',
	    'PocEjE RRJYJ R_KfK RcMkM R_QjQ R_TjT R[ZlZ RVCVU R_O_T RcCcM RdOdT RfVfZ R_OjOjT R\\HkHkJ RZSRU RbVbZ R^VjVjZ R^V^Z R\\H\\RZY',
	    'Po[HbH ReHlH RRJYJ R[KbK ReKlK ReVhV R`YhY Ri[l[ RVDVU R[D[[ R`S`Z R[DbDbK ReDeK ReRhRhY ReDlDl[ RbM^Q RaNgNfQ RcR_R RYTRV R`UbU_U',
	    'Po\\GjG R\\IjI RRJYJ R[LkL R\\P`P RePiP R[R`R ReRjR R\\UjU RYWlW RVDVU R\\D\\I RcLcS R\\DjDjI RZNZOlNlP RaW][ RZ[iZl[ RZTRV',
	    'Po`HjH RRJYJ R^KlK R`PjP R`RjR Ra[l[ RVDVU R`E`H ReCeK R`MjMjU R`EjEjH R[K]N R[D]G RZR]R]XY[ RcV_X R`M`UiVlX RYTRV R_Zf[',
	    'PoZElE RRJYJ R[LjL R[O`O ReOkO RZQ`Q ReQkQ RVCVT R[H[L R_U_Z R`H`M RcCbF RcNcQ ReHeM R[HjHjL RcUgYl[ RZSRV RbSZW RjTeV ReY[[',
	    'PoaEkE RSE_E RRI_I RaJjJ RaMjM RTN]N RaPjP RTQ]Q RaRjR RZU]U RbUlU RUXiX RR[l[ RTLTT RYCYF RTL]L]T R_U_[ RbJbT RaCaHjHjK R\\E[I RUEVI',
	    'PoeFlF R[FbF RRJYJ RZLcL ReNkN ReQkQ R[SbS ReTjT R[VbV ReWkW Re[l[ R^[a[ RVDVU R[O[[ R\\H\\K R^C^G R[ObOb[ ReNe[ ReCeIkIkM R`H`K RYTRV',
	    'PoYE^E RVJ]J RaTkT RUU^U RUW^W RUZ^Z R`[l[ RSHSJ RUQU[ RYCYG RTR^R^Z RSH_H_K RfPf[ R\\L_N R[N_R RVLSO RXNTS RbFdJhNlO RaEjEhJdN`O',
	    'Po[DlD RRJYJ R]KcK ReKlK RdUjU R[[l[ RVCVT R_T_Z RaGaQ RdRdZ RhGhQ R_KcN RhJfNcP RhJjO R`J]P RcG]H RjGdH RYSRU R[D[RYZ',
	    'PoRJXJ RfNlN RaUdU RUCUU RZDZV RXD_D^[ RaQaU R`NeMdO RhPh[ RkQkV RkKkO RkFhL RbJaN RbD`IdHbL RiDfI RXTRV RbZ`Z RhUkU RjHfI R_TXV RdPcX R^JYJ R^PYP',
	    'Po[ElE RRJZJ ReKjK R[NkN R\\QkQ R[TlT RVDVU R\\KaK`T R\\HbHbL RcCcF RgKgT ReHjHjL RdUhYl[ RcT[X RkVfX R^V^[ R\\H\\L ReHeL RYTRV ReY\\[',
	    'Po[DlD RRJYJ R^LjL R^NjN R]QlQ R^WjW R\\[l[ RVCVT R^G^L RbRbU RdGdQ RdUd[ R^GjGjL RiRkU R^R\\U ReRfU RaHaK RgHfK RYSRU R[D[RZY',
	    'Po[FlF RRJZJ R\\MbM ReMkM R]QkQ R]TjT R]WjW R]ZlZ RVCVU R\\I\\M R^P^[ R`C`G R\\IbIbM ReOeZ RgCgG ReIkIkM RdNfP R_NZS ReIeM RYTRV',
	    'Po\\HaH RdHjH RRJYJ RZJlJ R\\McM R]SbS R]UbU RVDVT R\\D\\H R]P]Y R_M_P R\\DaDaH RZPcPb[ RdDdH RdDjDjH RgVl[ RhVd[ ReQhY RYSRU RePkPhX RfLdT RbX[Y RZJZTYZ',
	    'PoTH^H R`HjH RTK^K R`KjK RTQ^Q R`QjQ RTT^T R`TjT RUWjW RR[l[ RTETK RTNTT RYEYK RYNYT RTE^E^K RTN^N^T R_U_[ R`E`K R`N`T ReEeK ReNeT R`EjEjK R`NjNjT',
	    'Po[EkE R\\JaJ ReJjJ RRJYJ R\\LaL ReLjL R\\QaQ ReRlR ReWlW RZVcV RYXcX Rh[l[ RVCVU RZGZJ RZSZV R\\N\\Q R^Q^[ RYObNbQ RZSbSbV RcEcL ReNlNl[ RZGlGlJ RYTRU ReNeUdZ',
	    'Po`FeF RaHdH RRJXJ RZK_K RaLdL RaOdO R\\UjU Rc[i[ RUDUV RZMZP R]M]O RaLaO RiMiP R[QjQjT RhCgG RjFfJ R\\C[G R^GZJ R_L_O RkLlO RgMfP R[T[WjWi[ R^GZG RjGfG RkJfJ RXURW RdJaJ',
	    'PoRMlM RT[j[ R_D_[',
	    'PoRPlP RT[j[ R`F`[ RhDXG',
	    'PoRGlG RTKjK RdWjW R]O][ R_C_K RTOjOjW',
	    'Po[MlM R\\ZkZ RYCY[ RcCcZ RRGVK RYRRU',
	    'PoTKYK R\\MlM RRQYQ R\\ZkZ RTDTK RZCZ[ RdCdZ RTQS[',
	    'PoSFlF RUJkJ R`C`J R`N`T RVNjNjU RVNVUT\\ RVUjU',
	    'PoRFlF RTKjK RX[h[ RXQX[ R_C_K RhWh[ RRORR RROlOlR RfRXU',
	    'PoRFlF RUKiK Rc[k[ RTOTS R_C_K RcRcZ RTOkOkS RkWkZ R[SYWWYR[',
	    'PoRFlF RTIjI RVPhP Re[k[ RSLSP R_C_I RYSeSe[ RSLkLkP RkXk[ RXYR[ RYSXX',
	    'PoRFlF RUKiK RVTiT RR[l[ RVOVT R_C_K RVOiOiT RYV[Z RdVcZ',
	    'PoSGkG RUMiM RRT^T R`TlT RS[^[ Ra[k[ RXOX[ R_C_M RfOf[',
	    'PoSFlF RUJiJ RR[l[ RRNRQ R\\P\\[ R_C_J RbPb[ RRNlNlQ RiSdW RVSXX',
	    'PoSFlF RTJjJ RVWiW RR[l[ RSMSQ RVQVV R\\M\\[ R_C_J RbMb[ RVQiQiV RSMkMkP',
	    'PoRFlF RUJiJ RUQiQ RR[l[ RRMRP R\\Q\\[ R_C_J RbQb[ RRMlMlP RVTXX RhUeX',
	    'PoRFlF RTIjI RVOhO RWVhV RR[l[ RSLSP RWRWV R_C_I RWRhRhV RSLkLkP RZX[[ RdXc[',
	    'PoSFlF RTJjJ RVQ\\Q RbWiW RR[l[ RSMSP RVQVW\\W\\[ R_C_J RbWb[ RbRiQiV RSMkMkP RbMbR R\\M[R',
	    'PocIiI RRJZJ RbMlM R]SiS R]WiW Re[i[ RVDVU R]P][ RdEdM R]PiPi[ R^IbM R[EkEkH R^G\\M RZSRU',
	    'PoSFlF RTIjI RVOhO RVS\\S RbWiW RR[l[ RSLSO R\\O\\S RVSVW\\W\\[ R_C_I RbWb[ RbObSiSiW RSLkLkO',
	    'PoSEkE RUHiH RWMgM RTPjP RRRlR R^UkU RUY\\Y Rd[g[ RUUUZ RUU\\U\\Y R_C_H R_M_P RhRh[ R`WbY RRKkKkM',
	    'Po`ElE RRG^G RSK]K RSS\\S R`TjT R`[l[ RSNSS RUUVX RXDXK RSN]N]S R`E`[ R`LjLjT R[TYY R^XRZ',
	    'Po[GlG RRJZJ RfNjN R\\PjP R\\SjS R[UlU Rb[g[ RVDVU R\\J\\R RfFfM RgSg[ R\\JjJjR R^W`Z R_DaE RaGaL_N RhDeG RZTRV',
	    'PoSEkE RUHiH RVPhP RRRlR R^UkU RUY\\Y Rd[g[ RUUU[ RVMVP RUU\\U\\Y R_C_R RhSh[ RVMgMgP R`WbY RRKkKkM',
	    'PoRElE RTHiH RSMkM RXReR RZXdX RUZiZ RSJSM RUOU[ RZTZX R[J[M R_C_H RbJbM RZTdTdX RUOiOi[ RSJjJjM R_P_U',
	    'PoXJSP RYJ\\QbWk[ R[CXK RYIgHdO]VR[',
	    'Po[OfO Rd[j[ RcUfX RVWR[ R\\RbT RYPTY ReP`VYY RYEYMVW RTEfEdLlLlQjZ RYHXT',
	    'PobZlZ RdCdV ReKkQ RZTT[ RWGSP R]LYV RWH^H[S RXDULWPZT]WaYhZ',
	    'PoXQhQ RSVkV RXQXV RaNa[ R\\CXHSJ RZF]JcLlO RZFfFbJ\\MRP',
	    'PoUPiP RVShS RSWkW R_N_[ R\\CXHSJ RZF\\IbLkO RZFfFbJ\\LRO',
	    'PoVUhU RVZhZ RVPV[ R_P_Z RVPhPh[ RWHSJ R\\CVJ RYG\\JbMjO RZFfFcI\\MRO',
	    'PoRTlT R_O_[ RXQZS RgPdS RaVjZ R\\VR[ R\\CWISJ RYG]KbNkP RYGfFaK[NSP',
	    'PoRFlF R_OcO R_C_F RcFcN RgIlN RWISM RZRSU R]F[LXO RZRfRcV^XXZR[ RZR]VbYj[',
	    'Po\\HSQ RVHYNbVl[ R^C[IgIdO]VR[',
	    'PoUIiI RRIfI R\\DWI RbFjI R[JWNRP R\\OXSTU RbIbNkMkO RYR]WbYj[ RYRfRbV]XT[ R^XR[',
	    'PoVFiF RRKlK R_C_K RcKcOkNkP R[KXOSQ R\\OXTSV RYS]WaYi[ RYSfRbV]YU[ R`Yl[ R^YR[',
	    'PoWGjG RWMhM RWPhP RYTgT RWIWP RWIgIgP RZCSJ R\\PYSRV RYU`Xj[ R_Xl[ RfT`X[YS[',
	    'PoUKiK RROkO R_C_O RWG[H RXERJ RfEaI ReGjI RZRRV RYSfScV^XV[ R\\OXS[VaYl[ R`XR[',
	    'PoSEkE RVKhK RVNhN RVQhQ RVHVP R^E^H RVHhHhP R\\QYTSW RYTgT_XZZS[ RYT\\WaYk[',
	    'PoWOhO RWQhQ RWThT RWLWS RWLhLhS RTHiHiK R]TSX RZERH R_IfJ RXFcE_JYK RVGVK R\\Yj[ RXVfV`YS[',
	    'PoZFcF R\\IbI R\\KbK R[QbQ RXTgT R[M[Q R[MbMbQ RVCVMYP R^C_F RhChMlQ RkFjJ RSGSJ RXFWJ ReGdJ RYTSV RYU^XbYk[ RVHUP RhGgO RfU`X[ZR[',
	    'PoRElE R[IcI RWIZI RfKkK R[LcL RaQjQ RTHTO RWFWO R^E^G R[G[NbNaQ R[GcGcN RfGkGkK R\\TTW R[QTS R[VaYl[ RfGfNkNkO RZNRO RZTeTbW]YS[',
	    'PoZDcD RTIYI R[IcI ReIjI R[LcL RTNYN ReNjN RaQiQ RTETN R[GcGcN ReDjDjN R^D^G R[G[NbNaQ R\\TSX RXDTE R[V`Yg[ R[QTS R_Yk[ RZTeTcW_X[ZR[',
	    'PoRFlF R[KcK ReLkL R[McM RaSjS RTITQ RWHWP R[I[PbPaS R[IcIcP ReIkIkL RXCZE R^F^I RfCdF R\\TTX RWKZKWK RYPRQ R[RTT RZV`Yk[ RfIfPkOkP RZUfV`YS[',
	    'PoZNdS R[HlHiNbUZYS[ R^DWLRN',
	    'PoeCe[ RaKhRlT RYDUKRO RUM[R RXH_H\\RYVT[',
	    'PofPkP Ra[k[ RaFaZ RaFkFkP RlWkZ RVO[TT[ RXDTN R^LYV RWH_H\\S',
	    'PogVlV RcEc[ RcElElU R[TU[ RTM]Q RXESP R^MZW RWH_H\\T',
	    'PoXJfJ R\\R`U R\\NWS RlWl[ R\\OdOaT]XXZ R^J[Q\\R RVEiEiTj[ RVEVPTY',
	    'PoXH`L R[RcU RbNVT R]ESJ R[FgFbKZORQ R`OlOgUaX\\YT[',
	    'PoWIgI RRMlM RVEgEgM R]MWR`V RYQRT RYQfQaV\\YS[',
	    'PoSGkG RXLX[ R_C_G RbNeQ R^N`ReXl[ R`LjLgRbWZ[ RYHWMRQ RaH_M[Q',
	    'PoVH\\H R_JkJ R_RkR R\\[l[ ReCe[ RUMZR RXVS[ RWHRQ R[NWX RWCTO R\\IYU RaE^O',
	    'PoSY\\Y RaZkZ RSRS[ RSR\\R\\Z RaQaZ RcDcP RkWkZ RXERK R^IYN RcHlL RWG_F\\K RWJZMRP RkS`U',
	    'PoUTZT RX[\\[ RULUU RULZLZT RdShV RcIfL RgM`T ReE`J RWCSL RcGjFhJdN`P RePlPjTfX_[ RUH^G^S\\[',
	    'PoVG]G RbUhU Rf[j[ RbLbW RbLhLhU RUJYK RWTZW RXDRI RZMST RcC_M R\\HWMSO RXP^P\\UXYR[ RbHlHlTj[',
	    'PoVG\\G R^GlG R^MkM R_ZjZ R_R_[ ReCeM R_RjRk[ RTHYJ RVSYV RXDRI RXORT R\\HWMRO RWQ]Q[UWYR[',
	    'PoRElE RUMiM RRPRS RUIUM RZCZG R[I\\M RbIbM RdCdG RUIiIiM RRPlPlS RYTaX R\\RTV R]PZSfScV_YV[',
	    'PoRFlF RUOiO RZUfU RRQRT RULUO R[L[O R_C_F RbLbO RULiLiO RRQkQkT R\\FYI RYU`X RSIfHjJ R]Q[TTW ReV_YU[',
	    'PoVNhN RVThT RVWhW RSLSO RVQVW R_J_L R_N_W RVQhQhW RSLkLkO RYFaH RdYl[ R[YR[ R`CTF R[EgEaHUJ',
	    'PoTI^I RTM^M RSQ_Q RTETM RYEY[ RTE^E^M RYT]V RgN`T RkSfY ReD`J RWTRY RbHfJ RdQlQiV RYPWU RdFjFiJeM`O RdShW_[',
	    'PoSF_F RUJ]J RUN]N RRR_R RSZ_Z RSRSZ RUCUR R]C]R RcIgK RjIeNaS RkTeY ReD`I R\\T^X RdFjFhK RdQlQiV RXTVX RfM`O RdThW`[',
	    'PoRLlL R_C_KcThYl[ R^M[UWYR[ R_E]Q',
	    'PoWLbL RRQlQ R`QbUeXl[ RZCWM RaM_T[XT[R[',
	    'PoRFlF RSNkN R_F_ObUfYl[ R_N\\UYXR[ R_K^T',
	    'PoRKkK R]UbZ R_C_KcThYl[ R^M[UWYR[ R_F]R',
	    'PoUHiH RSPkP R_C_PaUeXl[ R]SYXR[ R_K^S',
	    'PoRPlP RUHhHhP R_C_PaTeXl[ R]SYXR[ R_K]T',
	    'PoSNlN RUF`F_OaUeXk[ R^N\\UXXR[ R_H]S RhD`E',
	    'PoRPlP RVGVP RVGhGhP R_C_P]TYXR[ R_PaTeXl[',
	    'PoSGlG R`[e[ RbIhNlP R^C[JVORP R\\UXYU[ R^M\\U RWQgQgUfZ',
	    'PoRHlH RZOZV RdNd[ RYVU[ RaHfMlO R^C\\IWMRO',
	    'PoVIiI RRPlP RXDTM R_C_P]TYXR[ R_PaTeXl[',
	    'PoRHlH RWSgS R_N_[ RWQRS R[KTR RbHcLhQlR R_CZM',
	    'PoRIlI RXRfR RS[k[ R[KTR RbKgPlR R^CZM',
	    'PoRPlP RWE]H RTJZL RaUiY R`O^U[XT[ RaDaL_S',
	    'PoRHlH R[[_[ R_M_[ ReRiV RXSSX RbIgNlP R^C\\IVNRP',
	    'PoRK^K R`KlK RhWl[ RbV^[ RXDYP]W RfRjZ ReDeP`Y RWQSZ RXHZS RXKVW ReKcV ReHgV',
	    'PoRFlF RUOiO RfXjX RTJiJiN R_C_RaUdYl[ RTSkRjW RTNTS R^T[XWZR[ R_M^U',
	    'PoSGkG RYLeL RTPjP R_[d[ RbGgLlN R]C[GVLRN RZPXUfUe[',
	    'PoSHkH RRQlQ RfJeM RWJYO R`QaTeXl[ R]TYXS[ R_C_L^T',
	    'PoRHlH RSQlQ R_[e[ ReNe[ RWS\\X RaHdKlN R_C]HZKSN',
	    'PoRGlG RVYgY RVQV[ RVQgQg[ RaGfMlP R_C]HXNRP',
	    'PoSHkH R_O_Y RgNg[ RbHeKlN R^C]GXKRN RWNWSVX',
	    'PoSGkG RYMeM RTRjR RXZeZ RUZaZ RcTiZ RbGgMlO R\\RXZ R^C\\HVNRO RgYcY',
	    'PoRGlG RXM\\Q ReMkR RWOSR R`C_PaUeXl[ RXJUQ RfJcQ R_O]TYXR[ R_K`S',
	    'PoSHlH RWSgS RX[i[ RXNXZ R_N_S RWNgNgT RbHgMlO RjWj[ R^C\\HVMRO',
	    'PoSEjE RRRlR R_G_M RbIkM RaE^HRM R_QaVdYl[ R_T[XXYR[ R_O^V',
	    'PoSGlG RTMgM RV[j[ RVLV[ RaGdJlN RYPdU ReO`TfW R_EYKRN RaTXX',
	    'PoRTlT RVKVS RVKhKhS R[DTK RYFeF`K RaTdXl[ R^SZWR[ R_L^T',
	    'PoSGkG RXRiR RV[c[ RXMXX RlWk[ RbGfLlN RaQcWfYj[ R^C\\GWLRN R`LbU RcKXM R`WTX',
	    'PoRGlG RWRgR RWVgV R_[j[ RWMWX R_K_[ RVNhMgV RjWj[ RaGgMlO R^C\\HVNRO',
	    'PoSGkG ReWjW RUNUV RaNa[ RaNjNjW RaGdIjL R[XU[ R_DZIRL R]MUN R\\P\\UTV',
	    'PoRGlG RVMhM RXVgV RU[j[ RXMX[ RXQgQgV RcGeJlM R]EWKRM',
	    'PoSFkF RRNlN RUX`X Ra[g[ RURUZ RUR`R`X RgNg[ R_D_F_HTL R^HjL',
	    'PoSGkG RYMeM RURiR R[[_[ R_R_[ ReUjZ RYUTZ RbGgMlO R^C\\HVMRO',
	    'PoTFkF RUJiJ RRNlN RXRfR RUWiW R_O_[ RbJcMhRlS R^E[MTS',
	    'PoYEgE RWIiI RXLhL RWOjO RRUlU R`D`I R`J`O R_UcYk[ RTFTR`R^VZYR[',
	    'PoTGkG RWTgT RWYgY RWOW[ R_O_Y RWOgOg[ R_GdLkO R_C]IZLSO',
	    'Po_GjG RWQgQ RRUlU RWKWQ R_C_K RWKgKgQ R_U\\XYYR[ R_TdYl[ R_Q^W',
	    'PoTIjI RRMlM RdQd[ RWDZG RZWU[ RfDdG RcMfQlS R^E[LVRRS RZQYX',
	    'PoSGkG RURiR RVVhV RS[k[ R_J_R R_S_[ RaGgM RYMfLlN R^C\\HWLRN',
	    'PoUFjF RVIiI RRMlM RYQeQ RVUhU RbIcMhRlS R_UcYh[ R^C[MVRRS R_Q^U\\XU[',
	    'PoaOhO RRUkU RVHVS RbJbO RTJhJiS RZCRK RYFbF_I R[NWP R_TcYk[ R\\J[N R_R^V\\XYYR[',
	    'PoSF^F RSJ]J RfOjO RRTkT RXCXO R^TcYl[ R_R_U[XR[ R_EkEkIjO R^NRO RdEcL`O',
	    'PoRFkF RVMhM R_J_M R]MYQ RVP\\R RgQbViY RbFeJlL RbN`QUV R^EXJRM RcU[YT[',
	    'PoSGkG RdWgY RcGeJlM R_JUO R[LeLbNYR RcQYU R]DXJRL RZM`PTS R^TeWY[ RaRjRfV`YV[',
	    'PoSGkG RXMfM RRTlT R_J_Q RePe[ RYWT[ RcGfKlN R^EWKRN RYQXX',
	    'PoRFlF R^ObO RRTlT R_C_F RbFbO RgIkM RWISM R[KYNTP R_TcXfZl[ R_Q]UZYR[ R\\F[M',
	    'PoaFkF RRSlS RZCZO RTEVI RcC^H RkGgL`N RYJRM R`TdYl[ RaHeL\\O R_P]VZXWZR[',
	    'PoSFkF RYNfN RYQfQ RSTkT R[ZgZ RXZeZ RU[b[ RYJYT ReWi[ RbFgK R^DWKfJlM R[TXZ RYJRM',
	    'PoTHZH R\\HlH RRKZK R\\OkO RRTlT RTDTH RZCZP RdCdO RUKTP R_TcYl[ R_R^V[YS[',
	    'PoRLlL RWTgT RWWgW R_[j[ RWOWY R`N`[ RVQhPhW RTHhGjJ RkXj[ R\\DXH RcLfOlQ RcEhG R]JWPRR',
	    'PoRUlU RXE_F`I RTQiQjR RfFkK RcK`N RWGRK R]IYM RdNiP RVL^OYQ R_Q^V[YR[ R_UdYl[ RgC^D',
	    'PoRFlF R`PiP RTQ\\Q R`UiU RTV\\V Rd[i[ RULUW RSM\\M\\U R`LjLi[ R_W[[ RbFdIlL R^DYJRL R`L`S_X',
	    'PocFlF RTI\\I RcIkI RSM\\M RcMlM RSTlT RSF\\E\\P RcCcP R_ScXfZl[ R_T\\XXYR[',
	    'PoRFlF RYOeO RYRfR RZYdY Rd[h[ RZUZZ R_M_T RZUdUdY RULiKi[ R^DYJRL RbFdIhLlL RVKVRUX',
	    'PoSGkG RWNhN RXRhR RXVhV RXZkZ RXMX[ R`M`Z RYLSR R`J_N RcGeJlM R]EXJRM',
	    'PoRFlF RZ[][ Re[h[ RTL]L][ RaLiKh[ RbFeIlL RcOgP RcSfU RWS[U R^DYIRL RWO[P RUKUSTZ RaLaT_Z',
	    'PoYGeG RaNhN RVPhP RVShS RRUlU RVJVS RbGbM RVJhJhS R_UbY RYDTI R]EeEhHlI R\\LXN RcYl[ R]G\\M R_S^V[YR[',
	    'PoTEkE RWJhJ RWMhM RWOhO RSRlR RWGWO RdUd[ RWGhGhO R`E]G RZZV[ RdUlW R[UZZ R^Q[TWVRW',
	    'PoSFkF RXKcK RROlO RYVgV RYZgZ RYRY[ R]H]O RWSgSg[ RaPdQ RcGlK ReJaNTT R^CYHRK',
	    'PoTEiE RRIkI R`NlN R`QiQ RUR\\R R`ViV RUW\\W Re[i[ RUMUY RRN\\N\\W RiMi[ RbJjM R^E]HZKWLRM R`M`T_Y',
	    'PoRFkF RWMgM R[TcT R[WcW RUZjZ RUQU[ R[Q[Z RcQcZ RUQjQj[ RbFfJlM R_M]P R^EYJRM',
	    'PoXLfL RRTlT RVFVT R_H_P RVFhFhT RZH[J R^C^F R_KZP RdHbJ R]LaNeP R_SdYl[ R_TZYR[',
	    'PobEjE RWQYQ RfRiR RSUlU RYJYQ RdL`Q RSD]DYH RYJTO RcC_G RVF[H RRJ]J\\M RiF`J RbGfIlJ R_TbXeYk[ R_LjMiQ R_U\\XXYR[',
	    'PoXMfM RRUlU RUFUS R_H_Q RUFiFiS RYHcIcK R]MfQ R_L[PXQ R[J[K R_TcYk[ R^U[YR[',
	    'Po[KlK RcRgR RRUlU RXCXR RgJgR R^MaP R\\F]J RiFeJ RSEUI R[EbEbI RXLRO R_UcYk[ R_R^V[YR[ RiCaD',
	    'PoRFlF R\\RdR RYY^Y RbYgY RW[j[ RWLWZ RYTYY R\\O\\R RbTbY R\\OdOdR RbTgThY RbFfK RUMgLlM R^EXKRM RYT^T^Y',
	    'PoSFkF RXKgK RXMhM RXPhP RXSiS RSVlV R`[e[ RYJYS R`I`S ReSe[ RYW\\Z ReHlK R]CYHWLSN RZGRJ',
	    'PoRFkF R[O`O RdOiO R[RiR RdUiU R`K`O R[J[V`V`[ RdKdO RdUd[ RiJi[ RVKXM RSOVR RUWTZ RWTUX R^CZHRK RaFfIlJ R[QZY',
	    'Po`EjE RTIYI RRLYL R\\LlL RRUlU RTDTH RYCYR R^NbP R]GbH R`EfH RcC\\G R_TcYk[ RULSR RiFdI]K RgJgRcR R_R^V[YR[',
	    'PoRGlG RRJ\\J RbJlJ RTQ[Q RcQjQ RTT[T RcTjT RTMTT RWJVM RTM[M[T RcMcT RfJdMjMjT R_C_QaUdXl[ R_P]UZXR[ R_M`T',
	    'PoSFkF RYJjJ RXMgM RXOgO RXRiR RUWiW RUZiZ RUTU[ RXKXR R`I`R R_T_Z RUTiTi[ R]CYHVM RcGlK R[GRK',
	    'PoSElE R_JfJ RSMkM RUPkP RWS`S RYIYM R_H_M RkXk[ RXUWX R_U[Y RiScXgZj[ RaEkJ R\\S\\YW[ RdX][ R]CZGRJ RbMdX RUPUUTZ',
	    'PoUIiI RRP]P RaPlP RRTlT RRKRP RUDUI RVLUP RYLYP RRK]K]P RaKaP ReKeP RhKhP RUDiDiI RaKlKlP RbWk[ R[E[I RbEbI R_R^U\\XR[',
	    'Po[FcF RRIWI R\\HbH R\\KbK R\\QbQ RRUlU RSOSS RWOWR R\\N\\Q R\\NbNbQ ReOeS RhOhR RkFdM RXGRL RYOYQ R_C_F RkOkR RUCSH RgCeI R_TcYk[ R_S_V\\YS[ RXJYLRM RjJkMdM RiHdH',
	    'PoSF^F R`IdI RgIkI RTL[L RSO^O R`OkO R`SkS RUV]V R_VlV RUZ]Z RVRV[ RXIXO RTT]S][ R`D`I R`K`S R`DdDdI RfKf[ RgDgI R`KkKkS RgDkDkI RYF^K RWFSK RXCVI R\\LVRRT',
	    'PoRJlJ RgKdS`W[YT[ R^DVS`Vk[',
	    'PoRI]I RWDRS\\V R\\KZUTZ R_FkFiNeT`Y][ R`HbPeUk[',
	    'Po^FlF RRI]I Ra[f[ RfFf[ RZU^Y RZUT[ RSR[V R\\OXX RWDSS R]KZV',
	    'Po]EhE Re[j[ RXT\\X RYUS[ RSRYU R[OWX RVCSS RRI\\IYU RgMlMlSj[ RaEaM_W RhFgN',
	    'Po]NlN ReEe[ RWT[X RYUS[ RSRYU RZPVY RVCSR RRI[IYV RjD^F',
	    'Po^EkE R]NlN RdEd[ RYU\\X RYUS[ RSRZU R[OVX RVCSS RRI[IYU',
	    'Po`ZkZ R`F`Z ReCeU RVT[X RlVkZ RXUS[ RSRXT RZPVY RkI\\M RkIjSgT RVCSS RRI[IXU',
	    'PohWl[ RWT[X RXUR[ RdT][ RfQjY RSRXT RgKcV RZOVX RbC_N RaHhHeS RVCSR RRI[IXU',
	    'Po]IlI ReUk[ RYU\\X RXUS[ R_RdT RSRYU R[OWX RfTbY][ RiKfV RVCSS RRI\\IYT RbD_T RiIfT',
	    'Poc[k[ RcFc[ RYU\\X RYUS[ RlWk[ RSRZU R[OWX RVCSS RRI\\IYU RjD]F RhN^P RkNaO',
	    'Po]NlN R_[d[ RdJd[ R^EkEfJ RVCSS[V RRI[IXUS[',
	    'Po_YlY R_J_Y ReDeY RlJl[ RWT[X RXUS[ RTRYT RZOVX RWCTR RRI[IYU',
	    'Po_JlJ R][k[ RSJ]J]Z RUOZU RlXkZ R`OeRiU RXDXRTZ ReD`O RiKfRcU^W RXLVV',
	    'Po]JlJ RSM[M RSMSY RRE[E[M RgWkZ R^RhW RhNeUaY\\[ RcC_R RiJfU R[WRY',
	    'Pod[j[ RYU]X RYUS[ RaNcPeT RSR[V R[OWX RcC^M RVCSS RRI\\IYU RaIlIlRj[',
	    'PoRI^I RaXlX RaFaZ RaFlFlZ RSR]W R]NZVUZR[ RXCSS R^JZU',
	    'PoRI]I R`NjN R`[k[ R`N`[ R_EjEjP RSR[V RlVk[ RWDSR R]KYUT[',
	    'PoRFlF RRQlQ R_C_F R\\NZRWVjZ ReSaW\\YR[ RVFVMjM',
	    'Po^FkF R]ZlZ RdFdZ RVS[Y RSRWT RXUR[ RZOVX RVCSR RRI[IYV',
	    'Po[JlJ RYCY[ RhXk[ RbUgW RSGVK RYORU RhNdW R]ReTaY[[ RbC^R RhJeU',
	    'Po`OlO R_YlY R_FlFl[ RYUS[ RTR[V]W R[NWW RRI\\JYU RWCTR',
	    'Po\\UhU Re[j[ RTRZX RXVR[ RZPVY RVCSR RRI[IXV R_H^PlPj[ R]EiEhO',
	    'Pod[k[ R]I]M RdCdZ R]IkIkM RUSZX RSQUT RXUR[ RaU[[ RlWk[ RYPVY RdO`X RVCSR RRI[IXU RcKbU',
	    'Po]OlO R]ZlZ ReEeZ RSRZU RVCSS RjD]F RRI\\IYUS[',
	    'Po^IjI R]PlP RfVl[ RWT[X RbU][ RXUR[ RdCdPhY RSRYU RZPVX RcQ`X RVCSS RRI[IYU RdLcU',
	    'Po^JlJ R]RlR RhCh[ RXT\\X RXUR[ RSRZU RZOVX R^Z\\[ RVCSS RRI[IYU RaCaS`X',
	    'Po^ElE R]OlO RhEh[ RXU\\X RYUS[ RSRZU R[OWX RVCSS RRI\\IYU RaEaQ`W',
	    'PogMg[ RVS[X ReElL RXUR[ RcCaG\\L RSRXT RZPVY RVCSS RRI[IXU R`M`S_X',
	    'Po]IlI RUS[X RSRVS RXUR[ RcCdG RaNdUhYl[ RYPUY RhNeUaY\\[ R`IcT RiIfT RVCSR RRIZIXU',
	    'PoWT[X RXUR[ RfDjL RSRXT RZPVY RaD]M RcN]Z RVCSR RRI[IYU RdY]Z RgSjZbZ',
	    'Po^OkO R_[k[ R_E_Z RdEdN R^EkEkQ RVS[X RXUS[ RSRXT RlVk[ RZPVY RVCSS RRI[IXU',
	    'Po`RlR R`JlJlS RXT\\X RYUS[ RSRZU R[OWX RVCSS RRI\\IYU RkD`F`R^Y',
	    'Po]HkH RdCdN RXU\\X RYUS[ R_OaTfXl[ RSRZU R[OVX R]NjNhScX\\[ RVCSS RRI[IYU',
	    'Po]HlH Rh[l[ R`LhLhZ RlVl[ RVS[X RXUR[ RcCdF RSRXT RZPUY R]Z[[ RVCSR RRIZIXU R`L`S_X',
	    'Po^RlR R^I^T ReCe[ R^IlIlT RWT[X RXUS[ RSRXT RZPVY RVCSS RRI[IYU',
	    'Po]NlN RSRZU RVCSS RjD^E RRI\\IYUS[ RdEdNbU][ RdNgUl[',
	    'Po`LiL RXT\\X RdClK RXUS[ RcF]L RSRYU RZOVX R^QjQf[ RVCSS RRI[IYU',
	    'Po^FjF R]NlN RdYkY RaYhY R^YfY RWT[X RXUR[ RhRk[ RSRXT RZPVY RdN_X RWCTR RSI[IXU',
	    'Po`SdS RdCdR RiGlO RSRYU R`H\\O RiQhUdX][ RVCSS RRI[IXTS[',
	    'Po\\FlF RdId[ RUSZX RSQVT RgMlQ RWVR[ RcJ[R RYPUY ReFbL RZLWW RVCSR RRIZIXU',
	    'PoTIjI RRMlM RRQlQ RTDTH R_C_H RjDjH R[QWU R\\NZR RWVl[ RfScW^YR[',
	    'Po\\PlP R]HjHjP RfVk[ RUSZX RSQVU RXVR[ RbU\\[ RcDcQgX RZPUY RVCSR RRIZIXU RcLbU',
	    'Po[JlJ RTKYK RRQYQ RTDTK RZCZ[ RhWk[ R^ShW RhOeUbX\\[ RhKfU RbD]T RTQS[',
	    'Po[[l[ RjIjR R]EjEjN RUS[Y RSRVT RXVR[ RYQUY RVCSR RRIZIXV R]OiOiUh[ RcEbLaP`Z',
	    'PoVIhI RVNhN RRRlR RVEVM RVEhEhM R]NZRWVl[ ReSbW^YZZR[',
	    'Pog[l[ R^J^[ R^JlJl[ RVS[X RXUR[ ReCdMgPiU RSRXT RZPUY RdMaU RVCSR RRI[IXU ReGcQ',
	    'Po_LlL RgLg[ RUSZX RSRVS RXUR[ R[OVX RVCSR RRI[IYU RjD_F_Q^Y',
	    'Poc[g[ RTSZX RWVR[ R_W[[ R_J[N RfFjN RYQUY RaE^M RaR^Y RbO_X RVCSR R^OiOiTh[ RRIZIXU',
	    'PoRIZI R]KcK Re[l[ R]C]Z ReCe[ RlWl[ RUSZX RSRWT RWWR[ RXQUZ RkJeM RcYZZ RUCRS RYJWW',
	    'Po_[d[ RdMd[ RXT\\X RbHfL RYUS[ R]NlMjQ RSRZU R[OVX R^EkEgHcI RVCSS RRI[IYU',
	    'PoRPlP RUHWL RiFhJdM R]G_L RePdTaW^YU[ R[NYRWViZ RiC]EUE',
	    'Po\\HlH R]NjN R[TlT RcCc[ RUS[X RSQVT RWVR[ RYQUY RVCSR RRIZIWV',
	    'Po^EjE R\\MlM Rf[l[ RgMg[ RWT[X R_W[[ RXUS[ RSRXT RlWl[ RZPVY RVCSS RRI[IXU RaM_X',
	    'Po]HlH RdCdH RSRZU RVCSS RcHaW^[ RbOkOjVi[f[ RRI[IYUS[',
	    'Po^EjE R\\MlM Re[l[ ReMeZ RlXl[ RWT[X RXUR[ R_UZ[ RSRXU RZPVX RbP^X RVCSR RRI[IXU RcEcKaT',
	    'Po]EkE R]ZlZ R]E]Z RUS[Y RSRVT RWVR[ RdR_W R`IcLfOgRiV RiHdS RYQUY RUCSR RRIZIXU',
	    'Pod[j[ RXT[X RePiT R^EaH RXUR[ R`V\\[ RSRYU R[OWX RcP_Y RdCdL`W RVCSR RRI[IYU R^KlKlSj[ RdGbT',
	    'Po]ElE R`ZkZ RaMa[ RaOkOk[ RSRZU RVCSS RRI\\IXUS[ RbEaK]S',
	    'Po]JlJ RdTj[ RVT[X RSRWT RfDiG RfU`[ RXUS[ RaCaMeV RhOdX RZPVY R`R\\Z RVCSS RRI[IXU RaJ_V',
	    'Po^JkJ Rb[k[ RbMb[ R^EkEkJ RWT[W RXVS[ RlWl[ RSRYU RZPVY RjPbT RVCSS RRI[IYU R^E^Q]Y',
	    'Po_QlQ R_ZlZ R_I_[ ReCeZ R_IlIl[ RXT\\X RXUS[ RSRYU R[OVY RVCSS RRI[IYU',
	    'Po_IlI RcNjN RcTkT RcHc[ RVT[X RXUS[ RSRXT RZPVY R`D\\N RVCSS RRI[IXU',
	    'PofNkN R_ZkZ R_Q_[ R_QkQk[ RWT[X RXUR[ RcJ^N RSRXT RZPVY RdFaL RVCSR RRI[IYU R^ElEkN',
	    'Po_MjM R_UjU R\\ZlZ R_E_T R_EjEjT RWT[X RYUS[ RSRYT R[OWX RVCSS RRI\\IYU',
	    'PoUSZX RSQVT RXVS[ RiOkW R_IlIkM RZPVY R`O]W R`C^L RdId[a[ RVCSS RRI[IXV',
	    'PofKjK RbPkP RRSlS RbEbO RaEjEjK RkMkP RXERJ RWJZK RWG_F]KYNSQ R\\QWWk[ RfSbW_YR[',
	    'Po^IkI R^QjQ R]ZlZ ReIeZ RUS[Y RSQVU RXVR[ RcDeG RZOVY RVCSR RRI[IXV',
	    'Po\\HlH RRIZI R]QeQ R_YfY R\\ZdZ RVTZX RcCdG RaL]P RWVR[ RfO\\Y RSRXT RgLfP RYQUY RbH`N RVCSR RZJXV RiUjYcY',
	    'Po_IkI R\\PlP RfVk[ RVT[X RbU]Z RXUS[ RdCdQhX RSRXT RZPVY RcQaX R_E]M RVCSS RRI[IXU RdLcU',
	    'Po]ElE R^TeT Rb[i[ R^J^U R^JeJeT RiEi[ RVS[X RSRWT RXUR[ RZPVY RVCSR RRI[IXU',
	    'Po]HjH R\\OlO RdCd[ RSRZV RVCSS RRI[IXUS[ RdOaT]X RdOgTlX',
	    'Po\\HlH R]OkO RdCd[ RVSZX RgTlY RaS[Y RTSXUS[ RdMhU RZPVY RdM`U RVCSS RRI[IXU',
	    'PoSFkF RRLlL RUPhP RRSlS R_C_P RUIhIhP R]PZSWWkZ RfScW_YS[',
	    'Pob[i[ RbIb[ RiWi[ RVS[X RSRWT RXUS[ RdS\\[ RaDeE RjFcT RZPVY RiMlT R^M\\U RVCSS RRI[IXU',
	    'Po^NlN R^TlT R^H^U ReCe[ R^HlHlU RUS[Y RSQVT RXUR[ RZPVY RWCTR RRI[IXU',
	    'PoTFkF RRMlM RRQlQ R_C_G RYHZK ReHcM R\\NYRWVjZ ReSaW]YS[',
	    'PoRF^F RbNkN RRSlS RbFbN RbFkFkN RWKRO RYO]OXVk[ RXCVL R^G]O RgScW_YS[',
	    'Po]LlL ReEe[ RWTZW RSRVT RhTlX RXVR[ R`T]X RdKiV RZPVX RdK_V RVCSR RRI[IXV RjD^F',
	    'Po_UfU Re[j[ R_M_V R_MfMfU RWT[X RXUS[ RSRXT RZPVY RaC\\M RVCSS RRI[IYU R_HlHlSj[',
	    'Po^LjL Rd[l[ RcHdL RVT[X RWVR[ R`W\\Z RiN_WbX RSRXT RYQUY RVCSR RRIZIWV RkD^F',
	    'Po^ElE ReOlO R\\ZlZ R_L_Z ReEeZ RXU\\X RYUS[ RSRZU R[OWX RVCSS RRI\\IYU',
	    'Po^JjJ R^PlP R^D^Z R^DjDjJ RWT[W RXVS[ RSRYU RZPVX RVCSS RRI[IYU ReY[Z ReKgX RlXl[i[',
	    'Po^MkM R\\SlS RdCd[ RUS[X RSRWT RXUR[ R^F`K RjEhK RYPVY RVCSR RRI[IXU',
	    'Po\\NlN R]WlW Rd[h[ RVT[X RSRWT RbPfT RbGfK RXUS[ RZPVY RVCSS RRI[IXU R_EjEjRh[ R_E_L^O^T]X',
	    'Po[GlG RbNgN RSRlR RWFWO RgCgN R]J`L RYCRJ R]QXWl[ RgScW_YS[',
	    'Po_LhL RfYjY RbPb[ R\\PjPjX RVS[X RSRWT RdDlL RWVR[ RbE\\L RYQUY RVCSS RRIZIXU',
	    'Po\\GlG RgWkW R^K^W RdCd[ R^KkKkW RSRZU RXUS[ RVCSS RRI[IXU',
	    'PoeCe[ RVS[X RXUS[ RbT\\Y RSRXT RZPVY ReNbT RkXhX RVCSS RRI[IXU R^PlPkX R^I]R RkG^I',
	    'Po]MkL R_ZjZ R_Q_[ R_QjQj[ RXUS[ RSRZV RcC_M RVCSS RRI[IYU RjJlN',
	    'Po^NjN R\\TlT Rf[j[ R^H^[ RdCdT R^HjHj[ RUS[X RSRVT RXUR[ RYPUY RZLWW RVCSR RRIZIXU',
	    'PoRIZI R[OlO Rh[k[ R]D][ RbDb[ RfDf[ R]DkDk[ RUSZX RSRWT RWWR[ RYQUZ RUCSS RZJWW',
	    'Po[QlQ R^H^Q R^HjHjQ RfVk[ RURZX RWVR[ RdCdQgX RYQUY RcU_Y[[ RVCSR RRIZIXU RdMcU',
	    'Po]IlI R`QiQ R`YiY R`C`[ RiCi[ RVS[X RSRWT RXUR[ RZPVY RVCSR RRI[IXU',
	    'PoRI\\I R`LjL R`SjS R[[l[ R`E`[ R`EjEj[ RXT\\X RYUR[ RSRYT R[OWX RWCRS R\\JXV',
	    'Po]IlI R_ZkZ R_Q_[ ReCeQ R_QkQk[ RXU\\X RYUS[ RSRZU R[OWX RVCSS RRI\\IYU',
	    'PoRIZI R]E]V RUSZX RSRWT RgUkY RWWR[ RbHdM RYQUZ RiOfUcX][ RdS[V RUCSS RZJWW RjDjJhS',
	    'Po^JkJ R^RkR R\\ZlZ RdCdZ RXUS[ RSRZV R`F\\N RVCSS RRI[IXU',
	    'PoRIlI RRRlR R_D_O RaKlO R\\KRO R]OZRWVk[ RfRbW^YZZS[ RgC_EVE',
	    'PoYG_G RbNkN RRRlR RUEUN RYCYN RbCbN RlKkN RkFaI R\\QWVl[ ReSbW^YU[ R\\MRO R_MTN R_YR[',
	    'Po^ElE R^SjS R^ZlZ R^E^Z R^KjKjS RUSZX RSQVT RXVR[ RYPUY RUCSR RRI[IXV',
	    'Po[NlN R_[b[ Rg[j[ R]DbDb[ ReDjDj[ RSQYX RVWR[ RXQTZ RVCSR RRIYIWV R]D]T\\[ ReDeUd[',
	    'Po]IkI R\\QlQ RgHg[ RUS[X RSRVT R`DaH RXUR[ R`W\\[ RYPUY RiCfI RUCSR RRIZIXU RaIaR`W',
	    'Po_OlO R`ZkZ R^G^[ RfFfZ RSQYX RXRTZ R_C[M RkD`G RUCRS RRIYIWV',
	    'PoRIZI Rf[k[ RfCf[ RlWk[ RUSZX RSRVT R\\H^L RgPkT RWWR[ RaP]R RkHgL R_V[[ RXQUY RUCRS RYJWW RaCaQ`W',
	    'PobIiI RbThT Rh[l[ R^E^[ RbMbV RbMhMhT R^ElEl[ RVS[X RXUR[ RSRXT RYPUY RVCTR RRIZIXU',
	    'PoSGkG RUKiK RRNlN RRRlR R_G_N RXD[F ReDcF R\\OZRVW RWVk[ RfSbW^YZZR[',
	    'Po^JjJ R\\OkO RdCd[ RVSZW RSRWT RfSlY RWVS[ RaS[Y RdNeQgU RZPVY RdNaS R_E]M RVCSS RRI[IXU',
	    'Po]HlH R^NkN R_ZkZ R_R_[ ReCeN R_RkRk[ RXT\\X RYUS[ RSRZU R[OWX RVCSS RRI\\IYU',
	    'Po\\HlH RfUl[ RVS[X RTSXT R^MbQ RWVS[ RcI^M RZPVY RgL`S\\T RjPcW[[ RZLWW RdDcI RVCSS RRI[IXU',
	    'PobUhU Rh[l[ R^H^[ RbMbV RbMhMhU R^HlHl[ RVS[X RXUR[ RSRXT RYPUY ReCcH RVCSR RRIZIXU',
	    'Po^LlL R_ZkZ R_Q_[ ReEeQ R_QkQk[ RUSZX RSRVS RXUR[ RYPUY RVCSR RRI[IXU RkD^F',
	    'Po^JkJ R_QjQ R_UjU Rf[j[ R_M_[ R_MjMj[ RUSZX RSQVT RgFlJ RXUR[ RcD^I RZPUY RVCSR RRI[IXU',
	    'Po\\GlG RdCdG RXT\\X RhJlN RYUS[ R`J^N RaOcUgYl[ RSRZU R[PWX RhOeUaY\\[ RVCSS RRI[IYU',
	    'Po^KlK RaZkZ RaQa[ RaQkQk[ RXT\\X RYUS[ RSRZU R[OWX RVCSS RRI\\IYU R^E^P]X RjD^E',
	    'Po]GhG R\\MlM Ra[k[ RaPa[ RbCbM RWT[X RlXk[ RXUS[ RSRXT RZPVY RkDjG`Q[S RjR`U RVCSS RRI[IXU',
	    'PoSFkF R^RlR R\\EWJ RaVk[ RTV]Z R\\C[F RWNTV RdO`W RRR\\RZWXYR[ RiRhVeX_[ RXJjN ReGbK^MSN',
	    'Po`EkE R_OlO ReEe[ RUJ]M RUU_Z RXCUK RXOTV RRF]F\\JYLSO RRR^R\\VYXS[',
	    'Po\\FlF R]OjO R]JjJkN ReUjZ RWT[X RXUS[ RdCdRgX RSRXT RZPVY RdQbV_Y[[ R]N]RkRkWiW RVCSS RRI[IXU RdNeV',
	    'Po`HlH R`MkM R\\UlU R`M`U RfGf[ RUSZX RSRVS RWVR[ RYQUY RaC^L RVCSR RRIZIXU',
	    'Po\\ElE R\\MkM R]SkS R[ZlZ RdOdZ RWT[X RSRWT RgHlN RXUS[ RZPVY RbE_M RZLWW RVCSS RRI[IXU',
	    'Po^EkE R^SkS R^ZlZ R^E^[ ReEeK ReSeZ R^KkKkS RVCSS RRI[IXUS[ RSSZV',
	    'Po^EkE RbTjT R^ZlZ R^E^[ RbKbS ReEeK ReTeZ RbKjKjS RVT[X RTSXUS[ RZPVY RZLWW RVCSS RRI[IXU',
	    'PocJgJ RYQ\\Q RfQjQ RRSlS ReLaP RWMSQ R`LkLjP RXKVO RVEhEgI R_E\\HUJ RRL]L]QXWl[ RgScW_Y[ZS[',
	    'Po]ElE R_PjP R_UjU R\\ZlZ R_J_U R_JjJjU RXT[X RXUS[ RSRYU RZPWX RVCSS RRI[IYU',
	    'Po\\NlN Rf[l[ RdCdN RgNgZ RlWl[ RVT[X RSRWT R_W[[ RXUS[ R]F`K RjEhK RZPVY RZLWW RVCSS RRI[IXU RaN_X',
	    'Po_QgQ R_VgV Rf[j[ R_L_X R_LgLgV RVS[X RXUR[ RSRXT RYPUY RaC]M RVCSR RRIZIXV R_HlHlUk[',
	    'Po\\GlG R`LhL R]PkP Rc[g[ RVT[X RSRWT RgIlN RWVS[ RaH\\N RZPVY RcC_K RZLWW RaP_UiTg[ RVCSS RRI[IXU',
	    'Po\\NlN RdCdG R]GlGlK RVS[X RSRWT RXUS[ R_TkZ RZPVY RhPfVdX][ RbJ_U R]G]K RVCSS RRI[IXU',
	    'PoaZlZ RaQa[ R`RlRl[ RVS[X RbKeN RXUR[ RbF]K RSRXT RYPUY RcCaHjGhKbQ\\S RVCSR RRIZIXV',
	    'PoSD`D RfPjP RRRlR RcEcL RjCjO R[RVW R\\QYT RZDUJ[L RWH_H[MSP RXVk[ RfSbW^YU[ R_YR[',
	    'Po^HgH R^M^W RcHc[ R\\DjDkXl[ RSQXW RXQTZ R^MgMgWdW RVCSQ RRIYIWU R\\D\\RZZ',
	    'Po_MhM R^ZjZ R^R^[ R^RjRj[ RVS[X RSRWT ReFlL ReCfG RbF\\M RXUS[ RZPVY RcCaH RVCSS RRI[IYU',
	    'Po\\HlH R`QjQ R`UjU Rf[j[ R`K`[ R_MjMj[ RUS[Y RSQVT RXVR[ RYPUY RbG]P RbC_N RVCSS RRIZIXV',
	    'Po\\EcE ReEkE RdOlO RhEh[ RUSZX RWVS[ RYPUY RcN[P RVCSS RRIZIXV R`E`Q^W',
	    'Poc[l[ RdLd[ RVS[Y RSRWT RXUR[ RlXl[ RcCeG RZPVY R]G]K R]GlGlK RVCSR RRI[IYU RiK^M RiR^S RkQ`S',
	    'Po^IkI R\\OlO Rf[l[ RdCdO RgOg[ RlXl[ RVT[X RSRWT RXUS[ R_W[[ RZPVY R`E\\L RVCSS RRI[IXU RaO_X',
	    'Po`LjL R^ZlZ R^E^[ R^ElEl[ RXUS[ ReGeMiV RSR[V RVCSS RRI[IYU ReLcT`V',
	    'PobGjG RVS[X RSRWT RXUR[ ReC_I RYPUY RbIeL^P RiHeMaR^T RcTgW_[ RdPlPjTeX][ RVCSR RRIZIXU',
	    'Po]KlK RfUiU Rb[k[ RcNcZ RbNjNjU RUS[X RSRVT RXUR[ RlWk[ R_I[L RYPUY R`FgFeJ RVCSR RRIZIXV RaC^J^S]Z',
	    'Po_MjM R_SjS R]ZlZ ReMeZ RUSZX RSRWT ReElL ReCfG RXUR[ RcF]L RYPUY RdCbG RZLWW RVCSR RRIZIXU',
	    'PoRRlR R[RVW RTDYF RcJfMlO R^FkFiJ R^C\\H R\\O[R RYKRN RXVk[ RfRbW^YU[ RcFcJ`MZO',
	    'Po\\IlI R[PcP RTRZX RhDkF RWVR[ RhS`[ RjMfV RYQUZ RVCSS RRIZJWV RfCeLgY R_K_Q^W RlWk[i[',
	    'PoUHlH RWKaK RfDkE RXU`X R[KXU RUHTSSZ RWPaP^W[ZW[ RdDdMgZk[lW RjMfUa[',
	    'Po\\JlJ R[SlS R_D_R RhDhR RUS[X RSRVS RgVl[ RWVR[ R`V[[ RYQUY RZLWW RVCSR RRIZIXU',
	    'Po^GjG R]NlN R]SkS R[ZlZ RdCdM RdOdZ RVCSS[X RRI[I RZIXTS[',
	    'PoSIlI RRRlR R_C_O RVDYG RhDdG R[RVW R_HbLjO R_H[LSP RWVk[ ReSbW^YYZR[',
	    'Po\\EkE R[ZlZ RbEbZ RfEfZ RVSZW RSRVS RVWR[ RXQUY R]K^T RkKiT RYNVX RVCSR RRIYIXU',
	    'PoeGkG R[RlR Rf[l[ RfRf[ RkLkO RlXl[ RSQZX R`W\\[ RWVR[ R]HbGcKfNiO RYPUY R`CbK RaR_Y RiJcM\\N RVCSR RRIZIWV RhFaG',
	    'Po]JlJ RhQh[ RUS[X RfJlQ RXUR[ RbL]Q RZPVX RdFaN RVCTR RSI[IXU RaQ`X RiD`E',
	    'PoSFkF RRRlR R\\F\\N RbFbN R^DaF RfIkN RXJSN R]PWVkZ RfSbW]ZR[',
	    'Po^JjJ R^NjN R\\QlQ Rc[h[ R^F^M RdCdM R^FjFjM RWT[X RXUS[ RSRXT RZPVY RaQ_UjTh[ RVCSS RRI[IXU',
	    'Po]IjI R]NjN R]RjR Re[l[ R]E]R RfRf[ R]EjEjR RlWl[ RUSZX RWVR[ R_W[[ RYPUY R`R_X RVCSR RRIZIWV',
	    'Po^PkP R^UkU Rg[k[ R^K^[ ReCeK R^KkKk[ RWT\\X RXUR[ RkDjH R^D`I RSRYU RZOVX RWCSR RRI[IYU',
	    'Po]JkJ R]PkP R]TkT R\\[l[ R]D]O RdDdZ R]DkDkO RWT[X RXUS[ RSRXT RZPVY RVCSS RRI[IXU',
	    'Po]HlH RRIZI RTRZX RhDkF RWWR[ RgUb[ RkMfW RYQUZ R]NcNbV_W RlWk[ RVCRS RZJWV RfCfOhZ R]H]R[Z',
	    'PoRF]F R_IlI RTPXP RRSlS RXCXO R_E_L RgIgP R[SVW R\\QYT R]N[O RXWjZ RiD_E R\\JRK RfSbW^YS[',
	    'Po\\JlJ RdZkZ RdJdZ RUSZW RfDiG RWVR[ RlVlZ RYQUY R^S[Z RiLcO RjPcS RkTdW_X RVCSR RRIZIWV R`C`K^V',
	    'Po\\SlS R`[c[ RdQd[ RTRZX RXVR[ R^G`K RcGdK RjFhK RZPVY R]MiMgOdP RVCSR RRI[IXV RjC_E',
	    'PoRSlS RgElI RWKSO R^EYJ RTCXE RRGVH RgKbNYP RfSbW^YR[ RcDcK`J R]PXWjZ',
	    'Po_GlG R\\QlQ R]XlX Rd[h[ RbSeU RUR[X RbLeN RXVR[ R`C]J RYPUY RVCSS RRIZIXU R_JjJjUh[ R_M]X',
	    'Po^IkI Rd[l[ ReMe[ R^DkDkI RlXl[ RUSZX RSQVT RXVS[ RZPVY RVCSS RRI[IXU RiL_N R^D^Q\\Y RjP_R RjT`V',
	    'Po\\FlF R^QjQ Rf[l[ R^K^Q RdCdK RfQf[ R^KjKjQ RlXl[ RUSZX RSRWT R`W\\[ RXUR[ RYPUY RaQ_X RVCSR RRIZIXV',
	    'Po]GkG R^QkQ R^K^Q RdCd[ R^KkKkQ RTSYX RfSlY RSQWVR[ RbS\\Y RYPUY RdObT RYLVW RRIZIWV RUCSR',
	    'Po^LjL RdRkR Rc[l[ R^E^L RdLdZ R^EjEjL R_WbZ RTRZX RWVR[ R^T`Y RYQUY R^Q\\Z RVCSR RRIZIXU',
	    'PobMlM RbVkV Rb[l[ RgEgV RSRYX RXRTZ R_R\\Z R[F_E[N`N^W RjDbF R[Q^WaZh[ RUCRS RRIYIWV',
	    'Po^KjK R^PjP R^G^Z RdCdG R^GjGjP RkSeU ReY\\[ RVCSS[X RRI[I RZIXTS[ RdQeVj[',
	    'Po\\ElE R]SkS R[[l[ RdSdZ RWT[X RcMeP RgLjO RXUS[ RdGbL R_G]L`Q RSRXT RiGgL RZPVY RVCSS RRI[IXU',
	    'Po^IlI R^NlN Rf[k[ R^D^N R^DlDlN RWT[X RXUS[ RSRXT RZPVY RcV`Y\\[ RVCSS RRI[IYU R]RlRk[ ReDeNcV',
	    'PoaKjK R\\TlT RaDaK R^F^PiOiT RaDjDjK RWT[X RXUS[ RcSeVhYl[ RSRXT RZPVY RcRbW`Y[[ RVCSS RRI[IXU',
	    'PoRIYI R[KbK R[RbR R[EbEbZ RfEf[ RSRYW RfElEiM RXQTZ R^E^TZ[ RjNlSlVhW Rb[^[ RYJVW RUCRS R^N\\W',
	    'Po\\ElE R^ZjZ R^R^[ RdGdP R^RjRj[ RUS[X RSRVT RiJlM RWVR[ RfJjK RYQUY RfE`K\\L RVCSR RRIZIWV',
	    'Po\\PlP RcGdK RUS[Y RSQVT RXUR[ RiFlL R_Uk[ RYPUY RbM^V R_E]M RiPgVdXaZ\\[ RVCSS RRIZIXU RjD_E',
	    'Po_JkJ R_RlR R_VlV Rg[l[ R_M_[ R_D_J R_DkDkJ R_MlMl[ RWT[X RXUS[ RSRXT RZPVX RVCSS RRI[IYU',
	    'Po]EkE R`IjI R]NkN RaNaZ RgY^Z R]E]R[Z RVCSS[X RRI[I RZIXTS[ ReOgVl[ RjSgS',
	    'Po\\HlH R]NlN Ra[g[ RaRaZ RdCdN RhWgZ RcPfS RUSXVR[ RiSkY RYQUY R]S\\Y RVCSR RRIZJXV',
	    'Po^ZjZ R^S^[ R^SjSj[ RUSZX RSRVT RbIeK RdDhIlJ R]NiNeR RdC]K RXVR[ RYQUY RUCSR RRIZIWV',
	    'Po^MkM RhYkY RdHd[ R]HkHkM RWT[X R`DaH RiCgH RXUS[ RdR]Y RSRXT RZPVY RVCSS RRI[IYU R]M]SlRkY',
	    'PoTHZH R\\HlH RRKZK R]OkO RRSlS RTDTH RZCZP RdCdO R[SVW R\\QYU RTKSP RWWjZ RfSbW^YS[',
	    'PoZLlL R[[_[ R_E_[ RSQYX RhDkH RhTaZ RjNfV RXRTZ RbDZF RcRZS RfCfNgY RUCRS RRIYIWV RlWk[i[',
	    'Po^LkL R[SlS R^F^S RgLgS RgVl[ RTRZX RXVS[ R`V[[ RYQUY RUCSR RRIZIXV RhD^F',
	    'Po_RjR Rf[k[ R_K_Q RfRf[ R_KjKjQ RlXk[ RVS[X RTSXT RWVS[ R`W][ R_G\\K RaEhDjJ RZPVY RbR`Y RVCSS RRI[IXU',
	    'Po^IjI R^MjM R]PkP R\\UlU R^D^M RdPd[ R^DjDjM RWT[X RXUS[ RSRXT RZPVY RVCSS RRI[IXU',
	    'Po_RkR R_K_R R_KkKkR RaC^K RVCSS[X RRI[I RZIXTS[ RaFhFeK ReRcV_Z[[ ReSe[l[lX',
	    'Po]DkD R\\OlO R^ZjZ R^S^[ R]IiIiO R^SjSj[ RVS[Y RSRWT RXVS[ RZPVY RVCSS RRI[IXU RbE`O',
	    'PofMkM RbVlV RcZlZ RbJbV RgEgV RSQYW R^VYZ R_R[Y RXQTZR[ RVCSR R[E`E[M_M]W RkDbF R[P]VaYiZ RRIYIWV',
	    'Po]FdF RRIZI R\\JkJ RaQeQ R]O]Y RaLaY RTRYW RhDkH RVVR[ RlWk[ RXQUY RVCSR RYJWV ReXZZ RgCfPhZ',
	    'Po\\JkJ R^OjO R[TlT RVT[X RSRWT RgFlK RWVS[ RdReWhYl[ RZPVY RbC_J R_K\\Q RcU_Y[[ RVCSS RRI[IXU RdNcV',
	    'Po]HlH R^LjL R^PjP R^ZkZ R^S^[ R^SkSk[ RVS[X RcCdG RWVR[ RSRXT RYPUY RZLWW RVCSR RRIZIXU',
	    'PobKjK R\\TlT RbDbK R^F^OiOiT RbDjDjK RgVl[ RaV\\[ RVCSS[X RRI[I RZIXTS[',
	    'Po^G^[ RbDbU RSQYX RgVb[ RhSl[ RdHfM RiQfX RXRTZ R^FZO RgR_V R^D\\M RUCRS RRIYIWV RjDjKhU',
	    'Po^JjJ R]OkO R\\SlS R^E^J R^EjEjJ RWT[X RXUR[ RcSeWhYl[ RSRXT RZOVX RbV`Y[[ RVCSR RRI[IXU RdObW',
	    'Po_JkJ Rh[l[ R]N][ R_D_J R_DkDkJ R]NlNl[ RWT[X RdQjV RWVR[ RdR`V RSRXT RYOUX RVCTR RRIZIXU ReKdS',
	    'Po\\LeL Ri[l[ RbEb[ RgFgU RlDl[ RVS[X R`MeR RXUR[ RSRXT RYPUY RaK\\U RVCSR RRIZIXU RdD\\E',
	    'Po_MjM Rh[l[ R]I][ ReHeY RbElEl[ RTSZX RdNiS R]D`G RWVR[ RYPUY RdL`U RVCSR RRIZIXV',
	    'PoZEkE R\\KbK R\\QbQ R\\E\\W RbEb[ RSRYX RhVdZ RfQjZ RjPfY RXRTZ ReLiX RdIkIhV RbVZW RUCRS RRIYIWV',
	    'PoUH\\H RUK\\K RRSlS RbFcJgMlO R`FkFiIeM_O R\\RYTWWkZ RfScW_YS[ RRE^E R\\E\\OUOUE RRPUO',
	    'Po\\OlO R\\TkT R]ZkZ R_J_T R]EdEdZ R\\JjJiT RUSZW RSRWT RWVR[ RYQUY RVCSR RRIZIWV RjDbE',
	    'Po\\GlG R_LiL R_PiP R[TlT R_C_T RiCiT RgVl[ RUSZX RWVR[ R`V\\[ RYQUY RVCSR RRIZIXU',
	    'PobHlH R[K`K RbQhQ ReZlZ RbZjZ R[[_[ R[D`D`K RTRZX RSQUT RfCgG RjUl[ ReVcZ RjMeW RXQTZ RfIbQ RUCSR RRIYIWU R[K[Q`Q_[',
	    'PoRIYI ReVj[ RTRYW RhJkM RVWR[ RiIdN R`U[Z R`I[N RdRgX R\\IaK R[U`W RdHiJ RdEkEhK RXQTY R[EbE_K R[QbQ_W RcQkQiVfYb[ RVCSR RYJWW',
	    'Po\\FlF R]NkN R]SkS R]J]S RdCd[ R]JkJkS RTRZX RdRkY RdR\\Z RXVR[ RYQUY RVCSR RRIZIXV',
	    'Po_IkI R_MkM R^UlU R^ZlZ R^Q^[ R_D_M R_DkDkM R^QlQl[ RVCSS[X RRI[I RZIXTS[',
	    'Po^IiI R]MlM R^EiEiM R^PaS RVSZX RXUR[ RjPgS RdRiWlY RSRXT RYPVY RdU_W\\X RdMd[`[ RVCSR RRI[IXU',
	    'Po]ElE R_JkJ R^OlO R_TkT R]ZlZ ReFeO ReQeZ RUSZW RSRWT RWVR[ RYQUY RVCSR RRIZIWV R]E]Q[Y',
	    'Po\\DlD R^MeM RdQiQ RRSlS RTDTQ R^H^M R^HeHeM RiDiP R[SVW R\\QYT RYIZM RTDZDXH RZNWN RWWkZ RgScW^YS[',
	    'PobTgT Rf[j[ R[E[[ RbJbV RbJgJgT RbEkEj[ RSRYX RXRTZ R^L`S R[E`E^M R`U]U RUCRS RRIYIWV',
	    'PoRHlH RVKiK RUQiQ RRTlT RUNUQ RVEVK R_C_Q RVEiEiK RUNiNiQ R]QVX RWWk[ RfTcX_YT[',
	    'Po_JkJ R_NkN RiYlY R^Q^Y R_F_N ReNe[ R_FkFkN R^QlQlX RWT[W RWVR[ RdCdF RSRXT RYPUY RVCSR RRIZIXV',
	    'Po_LiL R\\QkQ R`[d[ R]G]J RdQd[ R]GkGkJ RVSZW RhTkW RWVR[ R_U[Y RcCeG RSRXT RYPUY RWCSR RRI[IXV',
	    'Po]GkG R\\NlN R^ZjZ R^R^[ RdCdG R^RjRj[ RUR[X RXVR[ R_H`M RiHgM RYPUY RVCSS RRI[IXU',
	    'Po\\QiQ Rf[j[ RcJcZ RTRZX RbQgU RWVR[ RaS\\X R^K_O RgKeO RYQUY R`C]J RcO`U RUCSS RRIZIWV R^HlHlTk[',
	    'PoRSlS RcCcI RTDXE RWKRP RRGVH R[FkFjH R^IaLdNkP R[IiIfLcN\\P R[F[M RfScW_YS[ R\\RXWk[',
	    'Po\\QkQ RdMd[ RcGdK RVS[X RSRWT RfSlY RWVS[ RdObS\\Y RiFkL R_E\\L RZPVY RVCSS RRI[IXU RiC^E',
	    'Po]FjF R[JlJ RdCdJ RUSZX RaL]O RWWR[ RaP\\V R_ScXk[ RSRWT RfJfOkNlO RYQUZ R_SiSdW`Z[[ RUCSS RRIZIXU',
	    'PohTkT R\\F\\J RdCdG ReKkKkT R\\FlFlJ R]PaT R^I\\R RVCSS[X RRI[I RZIXTS[ R^McMaT_X[[ RfLf[k[lX',
	    'Po\\GkG R]JjJ R\\OlO R`R`[ RfUkZ RSQZX RWVR[ RdPhX RYQUY RdCdO_S[U RkRfT ReY_Z RdMfV RVCSR RRIZIWV RdKeS',
	    'PocFkF RcQkQ Rg[l[ RdKdP RcCcI RbTgTg[ R]IkIkL RlXl[ RTRZX RWVR[ RaX^[ RYQUY RYMWW RVCSR RRIZIXU RbTaY R]I]S\\Z RfM_N RhMaM',
	    'Po^HbH RZMcM R[[^[ R^C^[ RSRXW RfVb[ RbQcV RfQjZ RiOeY RWRTZ RjJgV R[QYW ReHhV RcFkFhT RUCRS RRIXIWV',
	    'Po]FlF R\\OlO R^WeW Re[i[ R^R^Y R^ReReW RiOi[ RVTZX RTRWU RWVR[ RcIkL RYQUY RdHaK\\M RdCcJ RVCSR RRIZJWV',
	    'Po_IkI R_NkN R_SkS R_YlY R`G`[ RfHfY RWT[X RXUR[ ReDfG RSRYU RZPUY RaC]L RWCTR RSI[IXU',
	    'Po\\RjR R]J]R R\\KjKkR RUSZX RSRWT RgTiW R_G[K RXUR[ RYPUY R`C^I RaU^YY[ ReSe[lYl[ RVCSR RRIZIXV R_FgFeIdKbU',
	    'Po^IjI R^NjN R\\RlR R^D^N RdDd[ R^DjDjN RVSZX RSRWT RfTlZ RbS\\Z RXVS[ RZPVY RVCSS RRI[IXU',
	    'Po\\IlI R\\RlR ReEeO RUS[X RSRWT RWVR[ RdIhMlN RYPUY RdH`M\\N RbP`S_ViZ RhRgVdY\\[ RVCSR RRIZIWV RjD^F',
	    'PoeUj[ RVTZX RSQVT RhKkN RWVR[ RkFhI RkQhT RdQgX RcIiK RYPUY R_E^I R_Q]U RbL]N RcU`Y\\[ RVCSR RRIZIXU RdDcK RdOcV',
	    'PobFlF RbIkI RSI\\I RbMlM RRM\\M RRSkS RRF\\D\\P RbCbP R\\QWVl[ RfSbW_YR[',
	    'Po\\HaH RfHlH R\\NaN RfNlN R\\SaS RfTlT RbCb[ RfCf[ RTRZX RWVR[ RYQUY RVCSR RRIZIXV',
	    'Po[FlF RRIZI R[MlM R\\QjQ RcVjV Rc[l[ RdCdZ R\\JjJjQ RUSZX RSRWT RWWR[ RYQUZ R^T[[ RUCSS RZJWW R^VaZh[',
	    'Po^EjE R\\KkK R_[b[ RbOb[ RTSZX RfKlQ RWVR[ RaK\\Q RiRkX RYQUY RcE_N R^R]X RfRgW RUCSR RRIZIXV',
	    'Po^KcK ReKkK R^QkQ Rf[l[ R^E^P RfQf[ ReEkEkP RlXl[ RUSZX RSRVS RWVR[ R`V\\[ RYPUY RcD]E RaQ_X RVCSR RRIZIXV',
	    'Po]IkI R\\MlM RdWhW R_ZkZ R_P_Z R^RhRhV RVSZX RSQVT RfMlS R]D_F RVWR[ RjDgG RaN[S RkWkZ RXQTZ RcH_Q RdCaO RVCSR RRIYIWU',
	    'Po\\OlO R]TjT R^[c[ RcJc[ R]JjJjU RVTZX RSQVT RWVR[ R_FhFdJ R`E\\I RYQUY RZLWW RVCSR RRIZIXV',
	    'Po^IlI R^VjV R^ZjZ R^E^O R^Q^[ R^QjQj[ RiC^E RbN^O RVCSS[X RRI[I RZIXTS[ ReEgNlPlN',
	    'Po\\GlG R^QjQ R`[d[ R^J^Q RdQd[ R^JjJjQ RUSZX RjUkY RSRXUR[ R^T]X RcCeG RZPVY RVCSR RRI[IXU',
	    'PoSE^E RSL^L RSP_P RRSlS RSESP RSH^H^L RaEdJhNlP R`EjEhJdN`P RYLYP RYEYH R\\RWWk[ RfSbW^YS[',
	    'Po]LjL RdRjR Rd[l[ R]G]J RdLdZ R]GkGkJ RVSZW RWVR[ R_TaY RcCeG RSRXT RYQUY RVCSR RRIZIWV R^Q]Y',
	    'Po]GjG R\\KlK R\\QlQ R]VjV RcCcK RcQc[ RUSZX RSRXUR[ R_L`P RhLgP RZPUY RVCSS RRI[IXU',
	    'Po_KjK RaVhV R]ZlZ R]E][ RaPaV RdGdP RaPhPhV R]ElEl[ RVS[X RXUR[ RSRXT RYPUY RVCSR RRIZIXV',
	    'Po_PjP R_ZkZ R]G]J R_K_[ R_KjKjP R_TkTk[ R]GlGlJ RVT[X RXUR[ RdCeF RSSXU RZPVY RVCSR RRIZIXV',
	    'Po\\GkG RdCd[ RTRZX RdRlY RdOeS RhMlP RWVR[ R_MbObS[Y RiJfP RYQUY R_J\\Q RYMVW RVCSR RRIZIXU',
	    'Po^KkK R^PkP R\\UlU R^F^O RfPf[ R^FkFkO RVT[X RTSXUS[ RZPVY RcCcFdOaR]S R[LWW RVCSS RRI[IXU',
	    'Po]KbK RfKlK R]OlO RfRlR RbDbK R]D]TbSb[ RfDfK RfRf[ RlDl[ RVSZW RTSXUS[ RZPUY RVCSS RRI[IXV R]N\\Y',
	    'PoaJiJ R`NjN RaWhW Rh[l[ RaRaX ReGeN RaRhRhW R]ElEl[ RTSWTYVR[ RZPUY R]E]TZ[ RVCSR RRI[IXV R]N\\X',
	    'PodFkF R^NkN R^RkR R\\VlV R^J^R RdCdJ RdRd[ R^JkJkR RTR[Y RXVR[ RYPUY RVCSS RRI[IXU',
	    'Po^GjG R^KjK R\\N\\Q R^R^Y RdNd[ R^DjDjK R\\NlNlQ R^RjRjYgY RVCSS[X RRI[I RZIXTS[',
	    'Po]FkF R^IkI R\\LlL R^UjU Rg[j[ R^O^[ RdCdL RdOdU R^OjOj[ RWT[X RWVS[ RSRXT RZPVY RVCSS RRI[IXU',
	    'Po_KhK R]TjT R]O][ RaOa[ RfOf[ RUS[X RSRWT RdDkL RWVR[ RcD\\L RYQUY RZLWW R]OjOj[h[ RVCSR RRIZIXU',
	    'Po^IkI R]LlL R_PkP R^UlU R^E^I RdCdI RePe[ RlElI RUSZX RXUR[ RYPUY RVCSR RRIZIXU R]L]S\\Y',
	    'PoRG^G R_GlG RRRlR RXCXO ReCeO RZI]L RWGZJ RfIlN RWIRN RcI^N R\\PZSVW RXFVK RWVk[ RfSbW^YU[ R_YR[',
	    'Po]IjI R]MjM R^ScS Rf[l[ R]D]M R^O^[ RfOf[ R]DjDjM RlXl[ RTRZX RXVR[ RYPUY RlReT RcZ]Z RVCSR RRIZIXU',
	    'Po\\TlT R][k[ RdNdZ RcGdK RVT[X RSRWT RWVS[ RiFlK RZPVY R^E]L RVCSS RRI[IXU RiC^E RjM`N',
	    'Po\\DlD R]KbK RfTkT R[[l[ RbDbK R]K]TbTb[ RfTf[ RfDfKkKkS RWT[X RXUS[ RTSXT RZPVY RVCSS RRI[IXU',
	    'Po^JkJ R^OlO R`ZkZ R`S`[ ReKeS R`SkSk[ R^EkEkJ RUSZX RSQVT RXVR[ RYPUY RVCSR RRI[IXU R^E^R\\Z',
	    'PoSD^D RaDkD RRSlS RTHTO RTH]H]O RaHaO RaHjHjO RWJYN ReJfN R]P[SWWkZ RfScW^YS[',
	    'Po]NjN R[UlU R]G]T RbCbT RfCfT R]GjGjT RgVl[ RVSZX RSQVT RWVR[ RaV\\[ RYQUY RVCSR RRIZIWV',
	    'Po]FkF RTI[I R^OjO RUPZP RRTlT RTD[D[H RhMkO RSHSL[LZP RdCdFbI^JgJ^O R\\QWVl[ RfTcW_YS[',
	    'PoZFlF Re[l[ R_N_[ R`C`J ReKe[ RgCgJ RlWl[ RVSZW RWVS[ RSRXT R_NZQUY R`K]P RkMgQeRbT RVCSS RRI[IXU',
	    'Po\\ElE R`OhO R`VhV R]ZkZ R]L]Y R`I`V RdIdV R`IhIhV RkLk[ RUS[X RSRWT RWVR[ RYPUY RVCSR RRIZIXV',
	    'PoRSlS RTDTL RTD]D]L RaDaL RaDjDjL RgNkO RYM]O RVNSP RdN`P RfGeN RXGWN R]QWWl[ RgScW_YS[',
	    'Po]MkM R]RkR R\\VlV R]I]R RdId[ R]IkIkR RVS[X R`DaG RWVR[ RSRXT RZPUY RhCfI RVCTR RRIZIXU',
	    'Po^PjP R^UjU R\\G\\J R^K^W RdId[ R^KjKjV R]GlGlJ RURZX RcCeE RXURZ RYPUX RWCSR RRIZJXU',
	    'Po\\FlF R^MiM R^SjS R`[d[ R\\P\\S R^I^M RdCdF RdSd[ R^IiIiM R\\PkPkS RUS[Y RSRVT RXVR[ RYPUY RVCSS RRIZIXV',
	    'Po_HjH R_LjL R\\OlO Rf[i[ R_D_L R_DjDjL RVS[X RRRWT R`Q\\U RbU]Z RXUR[ RgRdZ RYPUY RbR`W RUCSR R`O^SkRj[ RRIZIXU',
	    'Po\\ElE R]NkN R[RlR R]H]N RaEaN RfEfN R]HkHkN RVSZX RWVR[ RSRXT RYQUY RbP`T^Vk[ RhTdY[[ RVCSR RRIZIWV',
	    'PobGlG RWPZP RRSlS RZIZP RVMRO RYITN RRI^I]L RbGdJgNlP RVF\\I RcCaI RiFhJdN_P RTE^EYG R\\QZSWWkZ RfSbW_YR[',
	    'Po\\GlG RRIZI R_QhQ R_UiU Rh[k[ R\\K\\[ RdCdK RdQdZ R\\KkKk[ RUSZX RWWR[ RaLaO RSRWT RgLfO RYQUY RVCRS RZJWV',
	    'Po\\FlF R\\MlM R`ZjZ R`S`[ R`C`J RhChJ R`SjSk[ RUSZX RXUR[ R`Q\\U RZPVY RcJ_S RVCTR RSI[IXU',
	    'Po\\KbK RdKkK R\\PbP R\\UbU R\\E\\[ R\\EbEcK RdEkEkK RgVl[ RUSZX RWVR[ RiUc[ ReQhY RdOkOgX RYQUY RVCSR RRIZIXU',
	    'Po_KhK R_[b[ R\\O\\[ R\\ObOb[ R]V`W RSQYX R^EcDjK RcD\\K RdTf[ RhTk[ R^R`S RfNdU RkNhU RXRTZ RUCSS RRIYIWV',
	    'PocHiH R^SjS R^WjW Rf[j[ R^O^[ RdDdL R^OjOj[ R[EkEkG R_F\\M RVCSS[X RRI[I RZIXTS[ R^IbMlM',
	    'Po^IkI R^MkM RcWiW Ri[l[ R\\Q\\[ R^D^M R^DkDkM R\\QlQl[ RUS[X RSRWT RXUR[ RhSjX RYPUY RVCSR RRIZIXV RdDeV_W',
	    'Po\\JlJ R]PjP R\\TkT R]XlX R]L]P R]U]X RePe[ R]FiFiJ R]LjLjP RUSZX RSQWUR[ RYQUY RbC`J RVCSR RRIZIXV',
	    'Po]FlF RgYjY R\\L\\O R^Q^X RdCdF RdNd[ R^QkQkX R]LlLlO RUSZW RSRXT RWVR[ R`GaJ RhGgL RYQUY RVCSR RRIZIWV',
	    'PobOeO R\\SlS RdPd[ RfHfN RUS[X RSRWT RdRkZ RWVR[ R]IkIjM RdR]Z RaFhI RYPUY R^DiEdG RdIaL\\N RVCSR RRIZIXV',
	    'Po]GkG R]KkK R\\OlO R\\TlT RUS[X RSRVT R_C`F RXVR[ RhCgF RdGdSeWhYl[ RZPVY RcV`Y[[ RVCSS RRI[IXU RdNbW',
	    'Po\\FkF R_JhJ R_NhN R[RlR R]ZlZ R]R]Z R_C_R RhChR RVTZX RSQVT RfTiW RbTaX RXUR[ RYPUY RVCSR RRIZIXU',
	    'Po\\ElE R\\TlT R]I]Q RaIaQ RfIfQ RTRZX RWVR[ RdSeWgYl[ RYQUY RcEaK RcV`Y\\[ RcRbX R]IkIkQhQ RVCSR RRIZJWV',
	    'Po]FlF RRI[I R]MlM R]RkR RTSYX RjUkY RWVR[ R`HbK RdCdE RZOUY R`T]Z RWDSR R[JWV RhHeMdZ`[',
	    'Po]IkI R^PjP R^TjT R\\WlW R\\D\\G R^L^T RdGd[ R^LjLjT R\\DlDlG RUSZX RWVR[ RYPUY RVCSR RRIZIXV',
	    'Po]IkI R]LlL R`RkR R_VkV R][l[ ReReZ R]DkDkI RVSZX RSQVT RgNkR RWVR[ ReL`Q RYPUY RZLWW RVCTR RRIZIXU R]D]S\\Z',
	    'Po\\HlH R\\PbP R\\TbT R`[b[ Rh[k[ R\\L\\[ R\\LbLc[ RfLfV RkKk[ RSRZY RVWR[ R_C`F RXQTY RhCfH RVCSR RRIYIWV',
	    'Po\\FjF R]JjJ R[NlN R_VhV R_ZhZ R_R_[ R^RiQi[ RUS[Y RSQVT RfNlS RWVR[ R_O[T ReJhQ RYPUY RbI^Q RcD`O RVCSS RRIZIXV',
	    'Po\\GlG R^SkS R^ZkZ R^M^[ RaDaK ReMeY RhDhK R^MkMl[ RVSZX RXUR[ RSRXT RYPUY RZLWW RUCSR RRIZIXU',
	    'Po^NjN R^RjR R^VjV R^J^V R^JjJjV RUSZW RWVR[ RaE\\I RfXkZ RSRXT RYQUY R`XZ[ RUCSS RRIZJWV R`EhEgIdI',
	    'Po^GhG R]LlL R`UjU R`ZjZ RaPa[ R_QjQj[ RUS[X RSRVT RWVR[ RjGbO RYQUY RcCdLaP\\R RVCSR RRIZIXU',
	    'Po^JkJ R\\MlM Ri[k[ R]Q][ R^E^J RbQb[ RdMcQ RdCdJ RfQf[ RkEkJ R]QkQk[ RUSZW RSQVT RWVR[ RYPUY RVCTR RRIZIXV',
	    'PoTD_D RbJjJ RTP_P RbPkP RRTlT RTDTP RWGWM RbDbO RbDjDjJ R[SWW RkMkP RZLZP RZDZH^H]LZL RXWl[ RfTaYT[',
	    'Po\\HlH R]OjO R]SjS R\\WlW R[[l[ R]L]S R]EdEdZ R]LjLjS RVTZX RSRVU RWVR[ RYQUY RVCTR RRIZJWV RjCaD',
	    'Po[FlF R_KiK R_OiO R\\SlS R_C_O RdOd[ RiCiO RdTkZ RdT]Z RVCSS[X RRI[I RZIXTS[',
	    'Po^JjJ R^NjN R]RkR R^VjV R\\ZlZ R^F^N RdRdZ R^FjFjN RUR[X RXVR[ RYPUY RcCbF RVCSS RRI[IXU',
	    'Po\\ElE R]ZkZ R]J][ RbJbZ RgJgZ R]JkJk[ RUS[X RSRWT RWVR[ RYPUY RdEcJ RVCSR RRIZIXV RfPbP RfTbU',
	    'Po^PiP R[UlU R^K^Q RgSg[ R^KiKiQ RWT[X RdCiH RWVR[ R`W\\[ RSRXT R`HgHlI RYPUY RcC^HZJ RVCSS RRI[IXU R`S_Y',
	    'Po\\FlF R[RlR R]L]R R`C`I RgCgI R]LjLjR RUR[X RXVR[ RcIcSeWhYl[ RYPUY RbU_Y[[ RVCSS RRIZIXV RcNbV',
	    'Po_JiJ R_RjR R_WjW R\\[l[ R]G]J R_N_W R_NjNjW R]GlGlJ RUS[Y RSRWT RXUR[ RdCdG RYPUY RVCSR RRIZIXU',
	    'Po]FbF RfMlM R^UjU R^ZjZ R]C]M R^P^[ RfCfM R^PjPj[ RlJlM RVT[X RSQVT RVVR[ RbNbP RYPUY RkEeF RZLVW RcL]L RVCSR RRIZIWU',
	    'Po\\HlH RRIYI R^KdK R_ScX RSRYX RhDjF RbU^Y RgVc[ RjOeY RXRTZ RlWj[k[ RkKgV RaK^T R]OdObV RfCfLfNhY RUCSS RYJVW R\\H\\SZZ',
	    'Po^JlJ RaRlR RaVlV RaZlZ RaMa[ ReDeJ RaMlMl[ R^DlDlJ R^D^Q\\Z RVCSS[X RRI[I RZIXTS[',
	    'Po\\KkK R[OlO R_RjR R]F_J RiFhIfK RbFdI R_SaUdXk[ RiTeX_[ RVCSS[X RRI[I RZIXTS[ RjC]E RaK_R\\Z',
	    'PodFkF R^NjN R^RjR R^VjV R^J^V RdCdJ R^JjJjV RVT[X RWVS[ RfXlZ RSRXT RZPVY RbX[[ RVCSS RRI[IXU',
	    'PoRI]I RRSlS RYDYO RXI\\M RfJjO RYIRO RlFhI ReGhM RaF`J RdKbN^O R\\PWVkZ ReDdL RgScW^YR[ R\\DSE',
	    'Po^HjH R^MjM R\\PlP RdUkU Rc[l[ R^D^L RdPdZ R^DjDjL R_T`WbZ RVS[X RTSXT RWVS[ RZPVY R_T\\[ RZLWW RVCSS RRI[IXU',
	    'Po[HlH RcLhL R^OkO R[SlS R^D^P RcCcL RcOc[ RhDhL RUSZX RWWR[ RcShXlZ RYQUY RcS^X[Z RZMWX RUCSS RRIZIXU',
	    'Po\\FlF R`IjI R^MlM R^SjS R^WjW Rf[j[ R^P^[ R^PjPj[ RUS[Y RSRVT RXVR[ R`H\\M RYPUY RbC^K RdIeM RVCSS RRI[IXV',
	    'Po\\FkF R]RkR R]J]R RdCd[ R]JkJkR RUSZX RSQVT RdQlY RXVR[ RbS\\Y R`LaP RgLgP RYPUY RVCSR RRIZIXU',
	    'Po`HjH R`LjL R_SkS R_WkW R_ZkZ R^D^L R_O_[ R_OkOk[ R^DlDlL RWT\\Y RXUS[ RSRXT RZPVY RVCSS RRI[IYU',
	    'PohOlO R\\SlS RdOd[ R`GhGiO RlLlO RUSZX RdRjY R\\D_E RWVR[ RcL_P R_WZY RcR]Y R^K]O R[G^H RYQUY RVCSR RRIZIXV RdEcM',
	    'Po^JjJ R^OjO Ra[h[ R^E^O RbSb[ RdEdO RhXh[ R^EjEjO RVT[X RXUS[ RiTlY RaPfR RSRXT RZPVY R^T\\Z RVCSS RRI[IXU',
	    'Po]KkK R]UlU Rj[l[ RbObZ RgOgZ R]FkFkK R]OlOl[ RUSZX RcCdF RWVR[ RYQUY RVCSR R]F]R[Z RRIZIXU',
	    'Po^HlH R^LlL R_SkS R_WkW Rg[k[ R^D^L R_O_[ ReDeL R_OkOk[ R^DlDlL RVS[X RXUR[ RSRXT RYPUY RZLWW RVCSR RRIZIXU',
	    'PodIjI RaWhW Ri[l[ R]N][ RaRaY RdIdN RaRhRhW R_DjDjN R_D^NlNl[ RWT[X RXUS[ RSRXT RZPVY RVCSS RRI[IYU',
	    'Po\\HlH RfRkR R]VkV R]ZkZ R]L][ RbHbQ RgGgR R]LkLk[ RVSZX RSQVT R`CaF RWVR[ RhDfI RYQUY RVCSR RRIZJWV',
	    'Po\\IdI RfKlK RfRlR R]WcW Ri[l[ R]P]Y R`C`O R]PcPcW RfDlDl[ RURYW RWVR[ RYQUY RVCTR RRIZJWV RfDfReY',
	    'Po^IiI R^NiN RZZlZ R]R]Z R^E^N RaRaZ RfRfZ R^EiEiN R]RjRjZ RVSZX RSRWT RWVR[ RYPUY RVCSS RRIZIXV',
	    'PocFkF R]MiM R]QiQ RcUjU Rb[l[ RcCcI RdQd[ R]IiIiQ RTRYX RWVR[ RYQUY R]TZ[ RUCSR RRIZIWV R]V`Zh[',
	    'PoaLkL R`PlP R^G^[ RaDaL RfLf[ RaDkDkL RSQYX RiUlY RcU_Y RfOiV RfObV RXRTZ R_CZN RUCRS RRIYIWV',
	    'Po]DlD RRTlT R^G^M R^GjGjM RcDcG ReNkP R\\LTP RdIbN]P RZIRJ RZDRF R\\RWVl[ RfTcW_YS[',
	    'Po_JhJ R]RbR R]VbV R`[b[ Rg[k[ R]N][ R]NcNc[ RfNfW RkMk[ RSQZX RWVR[ R`H\\J RdC^I RdDgHlI RYQUY RZMWW RVCSR RRIZIXV',
	    'PoRIZI Re[j[ R`UaY RVSZW RfTiW R^D_G R_OZT RXURZ RdUeX RSRWT RYOUX RdC^R R]U[Z R`NjNiR RZKWV R\\HhHhM R\\RlRj[ RVCSR',
	    'Po^LhL RgOkO R^XhX Rf[j[ R^T^W R^H^L RhHhL RhThW RUS[X RXUR[ R`O[T R_C]H RSRXT RYPUY RbM]O RbY][ RcHbM RVCSR RRIZIXV RcScY R^FlFkN R_RlQk[',
	    'Po`OkO R\\TlT R^ZkZ R^U^[ RdOdZ RkUk[ RVT[X RSRWT R^HbL RaN^R RbFjFdK RdC_H RXUS[ RZPVY RcGfJ\\O RVCSS RRI[IXU',
	    'PoRIZI R\\OjO R]TcT Re[l[ R\\F\\O R]P]Z RePe[ R\\FjFjO RlXl[ RUSZX RWWR[ RcCbF R_IgL RSRWT RYQUZ RfHcK_M RkReU RUCSS RZJWW RcY[Z',
	    'Po^JiJ R^NiN R^RiR Ra[h[ R^F^R RaUa[ RcCbF RgXh[ R^FiFiR RVSZW RSRWT RhUkX RWVS[ RaSeU RZPVY R^V\\Z RVCSS RRI[IXU',
	    'Po^FkF R]JlJ R^QjQ R^L^Q R`U`[ RdCdI R^LjLjQ RfVk[ RTRZX RXVS[ RdQhX RYPUY RfQ\\V RlTfV RdZ_Z RVCSR RRIZJXV',
	    'Po^IjI R\\MlM R\\SlS RVIXK RVPXR RgLlQ RhDlH RaL\\Q R`E\\H RfIhN RcF_O RcO_WjZ RcDaM RTEZEZ[W[ RhSgVdY\\[ RTETSS[',
	    'Po\\FlF R\\KlK R_OiO R_SiS R_XgX R\\XcX R_K_X RaCaI RhChI RiKi[ RSQZX RWVR[ RYPUY RVCSR RRIZIXV RlWfW',
	    'Po]DlD R`GjG R]JlJ R^TlT Re[h[ RaJaQ RhQh[ RaVdY RSRYX RWVR[ ReLhOlQ RYQUY RkLgN ReP`P RVCSR RRIZIWV R]D]Q\\Y',
	    'Po]IjI R\\MlM R]UjU R\\ZkZ R]P]U R]D]H RdMdZ RjDjH R]PjPjU RhWl[ RVS[X RSRWT RWVR[ RYPUY RcJ^K RVCSS RRIZIXU RdDcJ',
	    'PohNkN R]UjU R]ZjZ R\\E\\N R]Q][ RdQdZ R]QjQk[ RdElElM RTRZX RWVR[ RYQUY RgGdM RaHbK[N RbD\\E RVCSR RRIZJWV RgEfK',
	    'Po[KbK RZOcO R[E[K R^C^K R`O`X RbEbK RSRXW RgUb[ RfQjZ RiPeX ReKhW RfCcN RdW_X ReIkIhU RUCSS RRIXIVV R\\O[Y',
	    'PoRK^K RRTlT RYLYO RhIlHlI RXGYI RbLdNjQ RXCXF RbEaI R`KjKgN`Q RbDhEhI RUF]F]PZP R[SWWk[ RUFUKTP RfTcX_YT[',
	    'PoRIZI R]NjN RZ[l[ R]Q]Z R]D]N RaQaZ RfQfZ R]DjDjN R]QjQjZ RUSZX RSRWT RcIfK RWWR[ RYQUZ RdFaK_L RUCSS RZJWW',
	    'Po^EkE R^IjI R^MjM R^RlR Rf[k[ R^E^R RaUbY ReEeR RVSZX RhTiW RXUS[ ReTeX RSRXT RZPVY R^U\\Z RVCSS RRI[IXU RlSk[',
	    'Po\\FkF R]IkI R\\LlL R^SjS R\\VlV Rf[j[ R^O^[ RaCaL RdLdV RgCgL R^OjOj[ RWT[X RXUS[ RTSXT RZPVY RZLWW RVCSS RRI[IXU',
	    'Po^KjK R^PjP R^F^P ReFeZ R^FjFkP Re[lZl[ RVT[X R`W\\[ RcCcG RTSXUS[ RhRgW RZPVY RZLWW RVCSS RRI[IXU RaP`X RjTkWfW',
	    'Po\\FkF R]JjJ R[MlM R\\TkT RdPd[ RTSZX RfMkR RWVR[ RaM\\R RbThW RYQUY RcH_O RcT_X[Z RdDaN RVCSR RRIZJWV RfP_Q',
	    'Po]JjJ R\\F\\I RcCcF R\\FkFkI RjOfQ RcP[T RdSaVZY RVCSS[X RRI[I RZIXTS[ RdK`N[O RbLeOeVdZ_[ RePhTlX',
	    'PoRIZI R]JaJ RfJkJ R]NkN R]F]N RdCdR RfEkEkN RUSZX RSRWT RWVR[ R^SbWj[ RXQUZ RaD]E R\\RjRfWaY[[ RUCSS RZJWV',
	    'Po\\FkF R^LiL R^OiO R^SiS R[VlV R^I^V R^IiIiV RhXk[ RUSZX RSRWT RXUR[ RYPUY R`X[Z RcCbI RVCSR RRIZIXV',
	    'Po]DlD R`MkM R`QkQ Rb[e[ R`I`Q ReQe[ R`IkIkQ RTRYW RSQVT RWWR[ R`T^Y RiTkY RYQUY ReDdI RVCRS RRIYIXU R]D]Q[X',
	    'Po\\GjG R]KiK R[PlP R_TjT R]ZlZ ReTeZ RUSZW RSRWT R_C`F RWVR[ R`TZY RYQUY RbN^W RgCfG RVCSR RRIZJWV RcG`T',
	    'Po_ZiZ R\\G\\J R_S_[ RdCdG R_SiSi[ R\\GkGkJ RUS[Y RdMiRlS RgJkN RWVR[ RcM]S RaJ\\N RYPUY RVCSS RRIZIXV',
	    'Po^MjM R^PjP R\\TlT R\\E\\H R^I^P R^IjIjP R\\EkEkH RcReT RgVl[ RTRZX RWVR[ R`V[[ RYQUY RVCSR RRIZIXV',
	    'PoRRlR RSNSQ RSNkNkQ RXH]J R[RVW RjEhG RUETH RXHTK R]E[G RaF_H RdHkK RdHbJ_L RYCWJ ReCdI RXWk[ RfSbW^YZZS[',
	    'Po^GlG RbLkL R`RlR ReCeG R[H\\L R^QZS RbIaO R^G^R]Y RVCSS[X RRI[I RZIXTS[ RfLfSdWa[ RfShWl[',
	    'Po\\FlF R]IjI R[LlL R]PeP R\\TjT Ra[c[ RdCdL RdTd[ RUSZX RSRXT RhVkZ RhRkU RWVR[ R`V\\Z RYQUY RcN]P RgO\\T RVCSR RSIZIXV',
	    'PoRIYI R\\JbJ ReJlJ R][a[ Rg[k[ R\\DbDbJ ReDlDlJ RSRXW R_P\\S R`T[Y RiPeS RiTeX RXQTZ RUCSS RYJVW ReJeOlNlVk[ R\\J\\ObNbVa[',
	    'Po[GlG R[OlO R\\SiS R`G`[ ReGe[ R\\KiKiS ReSkZ R`SZZ RgCfF R^C_F RVCSS[X RRI[I RZIXTS[',
	    'Po]TcT RfTlT R]ZlZ R^O^[ RfNlNl[ RVTZX RXUR[ R^H_L RdGdK RSRXT RkGiL RZPVY RcN]O RVCSR RRI[IXU RkD`F',
	    'Po]FlF R\\PlP Rd[h[ R\\K\\O RdCdF RdMeP R\\KlKlO RUS[X RWVR[ RaW][ R`G`J RiGgK RYQUY RbQ`X RaUjUi[ RVCSR RRIZIXV',
	    'Po\\KcK R\\PcP R\\F\\Y R_C_G R\\FcFcP ReEe[ RSRYX RcRaU RXRTZ R_SbUcXZY ReElEiMlSlWhX RUCRS RRIYIWV',
	    'PoZIcI RZNcN RZDZN RZDcDcN RSRYX RjWl[ ReRkX RhVc[ RjQfY RWRSZ R[ZX[ Ra[^[ RdIkIiV RUCRS RRIXIVV R^D^O\\X RZRbRa[ RgCeS',
	    'PoSEkE RSLkL RTRjR RRUlU RTOTR R[L[R R`F`J RbLbR RTOjOjR RbGkI RaE\\GSJ R\\SWWk[ RdW_YS[',
	    'Po]EeE R]OkO R`RiR RcFdI R\\J\\LlJlM RUSZX RWVRZ R^VZZ R_F_I R`SeXl[ RjFhJ RYPUY R`P\\Y RiTeX^[ RaL^W RVCSR RRIZIWU RjCcD',
	    'Po]TbT ReZlZ R]Q][ R^H^O RfQfZ R]IjIjO RlWlZ RTRYW RWVR[ R`FgEeI RdJ`N RaD\\I RcLhN RYQUY RkSeU RcY\\Z RVCSR RRIZIWV',
	    'Po_KeK R_OjO R[UlU R\\G\\J R_K_U RfOfU R\\GkGkJ RSRYX RcCeF RVWR[ RfXkZ RXQTY RaXZ[ RVCSR RRIYIWV RhJaJ',
	    'Po]ElE RaIiI R_LjL R]RlR Ra[h[ R]E]R RaUa[ RhXh[ RcSeV RTSZX RiUkZ RdMaP RbF`J RWVR[ RdMhO RYQUY R]U[Z RVCSR RdHdN RRIZIXV',
	    'Po[DlD RRIZI R\\MkM R]PjP R[TlT R`[c[ R\\H\\M RaDaM RdTd[ RfDfM R\\HkHkM RUSZX RhVlZ RWWR[ R_V[Z RSRWT RYQUY RUCSS RZJWW',
	    'Po\\EkE RRIZI RaNhN R_VcV RfVkV R\\ZlZ R\\E\\[ R_Q_V RaHaM R_QcQcV RfQfV RaHhHhM RfQkQkV RUSZX RSRWT RWWR[ RYQUZ RUCSS RZJWW',
	    'Po^HkH R^LkL R^PfP RbTkT R]ThT R`[d[ R^D^L RdTd[ R^DkDkL RUSZX RhWkZ RhQlU RXUR[ RbN^P RdDdLaN R_W\\Z RYQUY RhO\\T RVCSR RRIZIXV',
	    'PocHlH R[HbH RgRkR ReWhZ RSQYX RdReX RXQTZ RdCbK R_[][ RhLgZl[ RcLkLjP RUCRS RRIYIWV R^C^H]P[Y R]MaMaU_[ RdPb[',
	    'Po^GjG RRIZI R^JjJ R\\QkQ R\\M\\Q R^D^J RaMaQ RfMfQ R^DjDjJ R\\MkMkQ RUSZX RSRWT RWVR[ RYQUY R_TaWfYl[ R\\TjTgWcY[[ RUCSS RZJWV',
	    'PoTD_D RWH]H RhHlH RUJ^J RTO_O RRSlS RTDTO RYL]M RWFVI RYKVN R\\QZSVW RbKdNjP RaJiJfN`Q RbDhDhH RbDaI RZGYM RWWkZ RfScW^YS[',
	    'Po]FkF R\\JlJ R^PjP R^SjS R\\WlW R^M^S R`FbJ RdCdF RdSd[ R^MjMjS RVSZX RSRWT RXVR[ RZPVY RhFfJ RVCSS RRI[IXU',
	    'Po]FlF R_MlM R`QjQ ReEe[ R`JjJjQ ReTjY R`SbU RUSZX RSRVT RWVR[ RjSgV RYPUY ReV^Y Ra[e[a[ RVCSR RRIZJWV R]F]S\\Z',
	    'Po]FlF R]LlL R_OjO R_TjT R_WjW R_Q_[ RdEd[ R_IjIjO RUSYW RSRWT RWVR[ RYQUY R_QjQj[h[ RVCSR RRIZIWV R]F\\R[Y',
	    'Po]ElE R`IkI R_NkN RbWjW R][l[ R]E][ RcPcW RcGcK RhGhK RaRjRjW RSRZY RWVR[ RdO_T RYQUY ReLbR RVCSR RRIZIXV',
	    'PoTE^E RbGlG RSI^I RUPkP RVSkS RYCYN RbGfKlN RYJ^L RcCaH RYHVLRM RiGeL_N R^R\\TZWj[ RfTcX`YW[ RUQTY',
	    'Po\\FlF R_OiO R`WhW Rh[k[ R]K][ R`S`Y RdCdF RdKdS R`ShShW R]KkKk[ R`GaJ RhGgJ RVCSS[X RRI[I RZIXTS[',
	    'PoSF\\F R_GiG R_JiJ RRJ]J R_LiL RfQkQ RRSlS R_D_L RfMfQ R_DiDiL RbN_Q RXCXLTP RXM\\O R[RYTWWkZ RfScW^YS[',
	    'Po\\DkD RdHjH RRIZI R[LlL R^PjP R`V`Z RdDdL RgUhX RUSZX RSRWT RWWR[ RdVeY RYQUZ R]V[Z Rj[f[ RUCSS RZJWW R_G_N]TkTj[',
	    'Po[FlF R_JgJ R\\PkP R\\TkT R\\WkW RZ[l[ R\\L\\P R_C_J RdJd[ RhChJ R\\LkLkP RTSYX RVWR[ RXQTZ RVCSR RRIYJWV',
	    'Po[FlF R]KjK R\\RkR R[UlU Rc[g[ RcCcR RgSg[ R]HjHjO R^VaY RUSZX R]H]OiOkS RWVR[ RYQUY RUCSS RRIZIXV',
	    'PoRIZI R_PiP R\\H\\K R]S]Y R_K_P RdCdH RdPd[ R_KiKiP R\\HlHlK RUS[X R^D_G RWVR[ RiDhG RSRXT RYQUZ R]SkSkYhY RVCRS RZJWV',
	    'Po]EkE R^HjH RRIZI R\\KlK R^QjQ R^TjT R^WjW R^N^W RdCdK R^NjNjW RVSZX RSQVT RWVR[ RgXl[ RYQUY RbY\\[ RVCTR RZJWV',
	    'Po[FlF R`IgI R]OjO R\\RkR R[VlV R]L]O R`C`I RgCgI R]LjLiP RTRZX RWVR[ RcIcUfYl[ RYQUY RcV`Y[[ RZLVW RVDSR RRIZIXU',
	    'PoZFcF R[PbP R[J[P R^C^[ R[JbJbP R^RbV RSQXW RgVb[ RgRjZ RiPeY RXRTZ R^P[X RfFfNiX ReIgU ReIkIhV RfCdN RUCRS RRIYIWV',
	    'PodDkDkM RVT[X RgFdH RWVR[ RiIeL R_F\\H R`I]M RdNlR RSRXT RYPUY R\\DbDbM`P[R RdR^T RfT^W RiWcY][ RVCSS RRIZIXV',
	    'Po[FlF R]NiN R]QiQ R[UlU R]J]Q R`C`H RgCgH R]JjJjQ RUS[X RSRVS RXUR[ RcSdWgYk[ RYPUY RbV`Y[[ RcQaX RVCSS RRI[IXU',
	    'Po]GlG R_MlM RbSiS RbIbS RcVcZ RdCeF RiIiS RSRYX RWVR[ RfVgZ RjVlZ RYQUY R_V_[ RZLVX RVCSR RRIZIXV R]G]S\\Y',
	    'PocEjE RaKgK R_LdL RcNkN R]VlV Ra[e[ RdCdN R_QfPe[ R`RaT RUSZW RSRWT RWVS[ RiRhT R]HlHkK RYQUY RVCSS RRIZIXU R]H]R[Z RiPcP',
	    'Po\\DkD RRIYI R^JaJ RfJiJ R]MaM RfMjM R]PjP R[SlS Rc[h[ R[G[J RcDcN R[GkGkJ RTRZX RVWR[ RXQTZ R_S^ViVh[ RVCSS RYJWW',
	    'PoTE^E RbGlG RRH_H RSK^K RRTlT RYCYK R\\QVX RWMSQ RbGdKgNkP RcC`K RiGhKeN`P RVN]M\\QYQ RWWk[ RfTcW_YT[',
	    'PoYGbG RZQbQ RZJZQ R^C^[ RZJbJbQ R^RbW RSRXW RhTk[ RhHgSdZ RWRS[ R^PZX RgPeX ReIkIjN ReCcN RUCRS RRIXIVW RgMhRfV',
	    'Po]FkF R\\LlL RaWhW R]ZkZ R]P][ RaSaW RdCdL RaShShW R]PkPk[ RUSZX R^HaK RWVR[ RiHfK RYQUY RVCSR RRIZIWV',
	    'Po\\FlF RRIYI R^LcL ReLkL RaHa[ RhHh[ RTRYW RaMcR RdCdF RXQTZ RhKkW RaK]W RgKdV RUCSR RYJWW R\\F\\Q[Y',
	    'PoRTlT RTHTQ RYCYP RTH^H^P RTDVF R^D\\F R[J\\O RbGdKgOlP RVJVN RdC`J RbGjGhKeN`P R]QWWk[ RfTcX_YT[',
	    'Po[FlF R]LiL R\\OkO R[TlT R]WkW RcCcH RcTc[ R]HiIiL R_Q`S RUSZX RSRVT RcLcO RhQeS RVVRZ RYPUY R]I]L RYLVW RVCSR RRIZIWU',
	    'PoRIYI R[NlN R\\TlT R^F^T RbGbT RaWb[ ReGeT R^HjGiT RUSZX RSRVS RVWR[ RfWf[ RiVk[ R_C\\K RXQTY R^W\\[ RUCSS RYJWV',
	    'Po^IkI R^LjL R^PjP R^TkT R_G_T RaWb[ ReFeT RUS[X RSRWT RWVR[ ReWf[ RiVk[ RYPUY ReDeI R^W\\[ R`C[LWW RVCSS RRIZIXU R_E^L',
	    'PoaFlF RdIkI RcMlM RcSkS RcWkW Rh[k[ R[D[[ RcPc[ RhIhM RcPkPk[ RSQXW RWRTZ ReDaL R[D`D^L`Q`U]U RUCSR RRIYIWV',
	    'Po]GkG R]JkJ R\\MlM R\\RlR R^ZjZ R^U^[ RdGdR R^UjUj[ RUSZX RSRVT R`D`G RWWR[ RhCgF R_O`Q RiNhQ RYQUY RVCSR RRIZIXU',
	    'Po]FlF RRIZI R\\JlJ R^RjR R^VjV R^N^V RaCaJ RdJdV RgCgJ R^NjNkV RgXjY RVSZX RSQVT RWVR[ RYQUY RaX\\[ RVCTR RZJWV',
	    'Po\\HbH RfHlH RRIYI R\\LbL RfLlL RaRgR RaVgV Rh[l[ R\\E\\[ RaOaX R\\EbEbL RfEfL RfElEl[ RTRZX RXQTZ RaOgOgYdY RUCSS RYJWW',
	    'Po\\HbH ReHlH RRIYI R\\LbL ReLlL R^QjQ Ri[l[ R\\E\\[ R\\EbEbL RdNdZ ReEeL ReElEl[ RTRZX ReSjX RdPfU RdPbT^X RXQTZ RUCSS RYJWW',
	    'Po]MkM R]QkQ R[UlU R]E]M RaWb[ RdEdT R]EkEkM RVSZX RSQVT RjXl[ RWVR[ R]XZ[ R_GaK RYQUY RgGfK ReWf[ RVCTR RRIZJWV',
	    'Po[GlG R^PhP R^ThT R_[c[ R^J^T RcTc[ R\\MiMiT RgVkZ RUSZX R\\I^K ReHkN RjIhK R^VZZ R`I[N RSRVS RXQTZ RbC_K RUCSS RRIYJWW',
	    'Po\\HlH RRIYI RdQlQ RdWlW RcCcN ReSdW RiOi[ RSQYX R^D`F RVWR[ RiDgG R]UaV[[ RcHgKlM RXQTY R^O\\T R^RbQ_X RcH`K\\M RVCSR RYJWV',
	    'Po[HlH RRIYI R]KiK R\\MjM R[PlP R]UjU R]XjX R[[l[ R]R]X RcCcP RcRcX R]EiEiJ R]RjRjX RUSYW RVWR[ RXQTY RVCSR RYJWW',
	    'Pog[k[ RbUcY RVT[Y RhUjW RhFlI RXUS[ RfUfY RcFdI RSRXT R^OjOiS RZPVY R_V^Z R^KgKgO RVCSS RRI[IXU R^E]J^SlSk[ R^M\\X RiC^E',
	    'PoRIYI R_KgK R]TiT RZZlZ R]O]T R]OiOiT RTRZX RfHlN RiDfG RVWR[ RjHhJ R\\G_IZN R_U`Y RdDgJ RXQTZ R\\EaE_J RgUfY RUCSS RYJWV',
	    'Po[GdG RZLkL R\\ScS R\\XcX R\\O\\Y R]H]L R\\OcOcX RSRYW R_D`G RiFjI RgUb[ RjOfW RlWk[ RXQTZ RaHaL RXMUX RRIYJVV RfCfMhY RUDSR',
	    'Po]DlD R_IjI R_KjK RRTlT R_GjGjN ReDcF R_G_NhOlP RZISJ R\\LWORP RZDSF RaO\\QXWjZ RfTcW_YS[',
	    'Po\\FkF R]IkI R_OiO R^VjV RZZlZ R\\L\\O R^R^U RdCdI R^RjRjU R\\LkLlO RUSZX RSRWT RWVR[ R`WaY RgWfZ RYQUY RZMWW RVCSR RRIZIXU',
	    'Po[FlF R\\JkJ R[NlN R\\SlS R\\WlW R_J_S R`C`H RcJc[ RgCgH RhJhS RUS[Y RSQVT RXVR[ RYPUY RVCSS RRIZIXU',
	    'Po`HhH R^OiO R^[b[ Rf[k[ R^K^N R\\QbQb[ R^KiKiN RdQkQk[ R]S_U RSQYX ReShU RdElH RXQTZ RbV[Y RkVdY RdCaF[H RUCSR RRIYIWV',
	    'Po]IkI R[LlL R]QkQ R]TkT R]WkW R]E]I R]N]W RdCdK R]NkNkW R]EkEkI RTRZX RWVR[ RfYl[ RYQUY RaY[[ RVCSR RRIZJWV',
	    'Po]FiF RRIZI R\\IkI R\\MbM RdMkM RdQlQ R[TlT Rf[l[ R_K_Q RcCcI RfTf[ RhKhQ RlXl[ RUSZX RSRWT RWWR[ RYQUZ R_Y[[ RaT_Y RUCRS RYJWW RbP[Q',
	    'Po[ElE R\\IkI R]OjO R[SlS R]ZjZ R]V][ R]K]O RcCcH R]KjKjO R]VjVj[ RgPfS R`P`S RVCSS[X RRI[I RZIXTS[',
	    'PoRFlF R[HcH RRKXK R\\PcP RRTlT RXHXQ R[C[H R\\K\\P R\\KcKcP RcCcH RfHfQlPlQ RkJfL RXNRP R\\RWWk[ RfTcX_YT[',
	    'Po\\IbI ReIkI RRIZI R]PjP R]SjS R[WlW R\\D\\I R]L]S R\\DbDbI RcLc[ ReDeI R]LjLjS ReDkDkI RUSZX RWWR[ RSRWT RYQUZ RUCSS RZJWW',
	    'Po[HlH R`OhO R`XgX Rg[k[ R\\Q\\[ R`K`O R`T`Y R`TgTgX R`KhKhO R\\QkQk[ RfHjL RVCSS[X RRI[I RZIXTS[ RiD_E RdEcH]L',
	    'Po[KbK R[NbN RZQbQ R[TbT RZXcX R[F[K R^C^K R^Q^X RbFbK RhWlZ RSQXW RgWc[ RfQjY RiQeY RWRTZ RdJhW RfCdL ReIkIhV RUCSR RRIXIWV',
	    'PodJlJ RdOkO RdVlV RZKbKbQ RhDhV R[I[QbQ`ZlYkZ RSQXW R]VX[ R]DZL RWRTZ R\\FaF_K_R[Y ReFdM RUCSR RRIYIWV R_M^V',
	    'Po\\JlJ R^ViV R^ZiZ R^Q^[ RdDdP RdQdZ R^QjQj[ RUS[X RSQVT RWVR[ RcJ]P R_F`I RdJiOlP RYQUY RhFfJ RVCSR RRIZIWV RiC^E',
	    'Po_JgJ R]SbS ReSjS R]M]R R]MbMbR ReMeR ReMjMjR RhWl[ R_VaY RVSZX RSQVT RWVR[ RdC^I RdDgGlI RgTeZ RYQUY R_T]Z RYMVX RVCSR RRIZIXU',
	    'Po]FkF RRIZI R\\JlJ R^PjP R^TjT R^M^T R_V_[hZh[ R^MjMjT RbUeX RURYW RWWR[ RgGfJ R`G`I RjVlZ RcCcF RYQUY R\\V[Z RVCSR RZJWV',
	    'Po`IgI R]QkQ R^WjW R^ZjZ R]K]Q R^S^[ RdKdQ R^SjSj[ R]KkKkQ RVSZW RSQVT RWVR[ R`MaO RdElH RgMgO RYQUY RcC`G[I RVCTR RRIZJWV',
	    'Po[JbJ R[NbN RZQcQ R[F[N R]C]F R[FbFbN RUSYW RgWc[ RfQjZ RiQeY RSRWRTZ Ra[][ ReKhX RjLgW ReJkIhV RVCSR R^O^Q[Y RfCdN R]UbUa[ RRIYIWV',
	    'PodJlJ RdOkO RdVlV RZKbKbQ RhDhV R[I[QbQ`ZlYkZ RbVcY RSQXW R]VX[ R]DZL RWRTZ R\\FaF_K_R[Y ReFdM RUCSR RRIYIWV R_M^V',
	    'PoRIZI R\\IkI R]LjL R[OlO R\\TkT R[WlW R\\D\\I RaDaI RcIcO RcTc[ RfDfI R\\DkDkI R`Q`T RTRYW RWWR[ RgQfT RYQUY RVCSR RZJWV',
	    'Po`EkE RTH]H R_JlJ R_MkM RUO]O RRSlS RUKUO RTD]D]H RUK]K]O ReCeF ReJeP R[SWW R\\QYT RbGbI RhGhI RXWjZ RTFSN RfScW^YS[',
	    'Po[ElE RRIZI R`MgM R]OjO R^UiU R^XiX RZ[l[ R]H]O R^R^X RcCdF R`J`LgJgM R^RiRiX R]HjHjO RUSZX RSRWT RWWR[ RYQUZ RUCSS RZJWW',
	    'Po`HlH RRIYI Ra[l[ RhOhW RURYW RgOlT RbCcG ReHaM RdP`Q RfMbQ RZM]M]XZZ R[E]I R]XcZ RXQTZ RiCgH RkKgOcT_V RgXcX RVCTR RYJVW RgMhU',
	    'Po\\FkF RRIZI R[NlN RaWfW R]ZjZ R]Q][ RaTaW RcCcN RaTfTfW R]QjQj[ RUSZX RSRWT R_IaL RhJjL RWWR[ RYQUZ R_H]L RhHfL RUCSS RZJWW',
	    'Po\\FlF R^KlK R^QlQ R_TjT RbIb[ RgJg[ R_MjMjT RUSZX ReTkZ RWVR[ RbU]Z RdCdF RYPUY RVCSR RRIZIWV R\\F\\U[Z',
	    'Po]IkI RRIZI R[LlL R^SjS R]D]H R^N^S R`V`Z R^NjNjS R]DkDkH RSQYW RWWR[ RdSeVhYl[ RYQUZ RbT[X RkUfW RaDaI RfDfI RUCRS RZJWW ReZ][',
	    'PoSE_E RRI_I RTN]N RTQ]Q RRTlT RTKTQ RVEVI RTK]K]Q RYCYE RgMkQ RdM`Q ReHhO RbGkGjJ R\\E\\I RcC`J ReGcN R[RWWk[ RfTaYT[',
	    'Po\\HiH R\\JiJ R\\MlM RZShS R^U^Z R\\EiEiJ RSQXW RaRcW RgUbVeYj[ RXQTZ RaR_UZW RjWgW RcZ[[ R\\E\\OkOjW RUCRS RRIYIWV',
	    'Po[FlF R[LlL R]RjR R]UjU R\\WkW RZ[l[ R]N]U R`C`G R\\IeIdZ RgCgG R]NjNjU RTRYW RVWR[ RXQTY RVCSR RRIYJWV RjHcI',
	    'Po]FkF R^JjJ R\\MlM R[SlS R][`[ R`O`[ RdFdM RVSYW RSQVT RiOkQ R_C`F RWVR[ RhCgF RlYl[ RYQUY RjUfYc[ RfOhY RVCTR RRIZJWV RcO\\P RdV[W',
	    'Po_IhI RRIZI R\\PbP RePkP R\\SkS R\\K\\O R_D_I R\\KbKbO RdQd[ ReKeO R_DhDiI ReKkKkO RUSZW RSRXT RWVR[ R`W[Y RdS^X RdSiXlY RYQUY RVCSR RZJWV',
	    'PoRIYI R`QgQ RaMaQ R\\JkJkM RSRYX RiMlP RaR\\W R\\J\\M\\P R]F^I R\\EcEcH RiFhJ RXQTZR[ RbLeL R_TbXfYl[ R_TiTeXbYZ[ RVCRS RYJWW RjDbD',
	    'Po[FkF R[LkL R\\PjP RZSlS R_V_Z R`K`S RcCcF RfKfS RdUhYl[ RfHkJ R`H[J RdSZX RkUfW ReY[[ RVCSS[X RRI[I RZIXTS[',
	    'PoaLgL R[RlR R\\UlU Ra[d[ R^N^R RaIaL RbNbR RdUd[ RfNfR R\\FkFkI RUSZX RSRWT RbHeJ RcDeF RiIkL RWVR[ R\\F]I]L RYQUY R^NjNjR RVCSR RRIZJWV',
	    'Po]FlF RfJlJ R^JdJ R_VfV RhHhQ RTRYW RaKdN RcCeE RWVR[ RbHbQ_V RhTeV RhIkO RhHfO RYPUY RdW^Z RaH_O RVCSR RRIZIWV R]F]R[Z RiWkZ`Z',
	    'Po^QlQ R^SkS R^WkW R^ZlZ R_O_[ R\\DcDcL ReOeZ ReDkDkL RUS[X RSRWT RiDeH RWVS[ R_O[S RjHeL R`D\\H RaH\\L RYQUY RVCRS RRIZJWV',
	    'Po[ElE R\\HkH R]MiM R\\OjO RZRlR RbUlU R[YaY Re[i[ R[T[Z R[TaTaY RcCcH RcMcO RiSi[ R[KkKkM RdWfX RTRYX RVWR[ RXQUY RUCSR RRIYIWU',
	    'Po\\HlH RRIYI R\\KiK R[NlN R[[l[ R\\T\\Z R`P`S RaTaZ RcCcN ReTeZ R\\EjEjK R\\TjTjZ RTSYX RiPlS R\\P[S RdPeS RXQTZ RUCSR RYJWV',
	    'PoZFlF RdSlS R[SbS R`C`H RfCfH R`V`[hZh[ RaUeV RTRYW RiVlY R[L_O[R RjLgO RgIeLhOdR R^I\\M RaL^O RaPbS RjOlT RXQTZ R\\VZZ RUCSS RRIYIWU',
	    'Po\\J_J RfJjJ RfPjP RZUlU R]E]U RaOaU RaCaKeKeU RaGfFjEjU RhWl[ RSQYX R^YZ[ R_W\\Z R_D\\F RXQTZ R\\O_O\\P RVCSR RRIYIWV',
	    'Po^HiH R^QjQ R^UjU R^XjX R_N_X RcCcF RcHcM R[OjOjX R\\F\\GkFkH RUSZX RSRWT RWWR[ RgYl[ RYQUZ R`J[L RhK[O RaYZ[ RUCSS RRIZIXU',
	    'Po]RkR R\\N\\Q RdPd[ R\\NkNkQ RUSZX RdRkY R_IcJ RgHkL RkEjH RbT\\Z RcEbH RXUR[ R]E]H RYPUY ReFeI RZLWW R`C_K RhDfK RVCSR RRIZIXU',
	    'Po\\DkD R^JaJ RfJiJ R]MaM RfMjM R[PlP Rh[k[ R[H[J R\\T\\[ RaTa[ RcDcN RfTf[ R\\TkTk[ R[GlHlJ RSQZX RXQTZ RcPaU RUCRS RRIYIWV',
	    'Po[DlD RRIZI Rh[j[ R]I][ RcDc[ R[KkJk[ R^TbX RUSZX RSRVT RgFlJ RWWS[ R^MaO]Q RaS_X RhLgP ReMhOeQ RhSfX ReThWeY RYQUY RVCRS R`F[JWV',
	    'PoTDlD RaJkJ RVTlT RgMkQ RiFjH ReMaQ R\\O_O RWLWPZOWN_M_P\\O R\\Q_R[WkZ RfTcX`ZX[ RXJ[J RTDTRR[ RfGeN RWFXI[HWG]G_G_J[J',
	    'Po[ElE RRIZI R]MiM R]QiQ RgRiU R]UiU R\\WjW RZZlZ R[O[R R]J]M RcCcH RcTcZ RiJiM R[OlOlR RTRYW RWWR[ RYQUZ RdQ]T R\\HhIiJ R`J]J RVCSR RZJWW RbJ_J ReJbJ RaJdJgJ',
	    'PoTG]G RaGjG RTJ]J RaJjJ RTM]M RaMjM RRSlS RTDTM RTD]D]M RaDaM RaDjDjM RZN]O RUNSQ RbN`Q RhNkP RgSbYT[ R\\RWWk[',
	    'PoYEbE RdIlI RZJbJ RZMbM RYObO RRTlT RZGZL RZGbGbL RiIiQ RTDWE RWMSQ RRHVI R]C]QXWkZ RkDdFdLcQ RfTcW_YT[',
	    'PoSI[I RdIlI RSN[N RdNlN RV[Y[ Rh[k[ RSDSN RSD[D[N RdDdN RdDlDlN R]RcX RfWb[ RaR^Z RcRlRk[ RWDWPUX RSRZRY[ R_C\\S R\\IbI`W RhDhOfX',
	    'Po[FlF RRIZI R]KkK R]MjM R]OjO R]RkR R\\UjU R^I^S R`C`G ReIeR RfCfH RVSZX RSQVT RWVR[ R^I[M RYQUY R^VcYk[ RiVdY[[ RVCTR RZJWV',
	    'PoTEkE RUHjH RVMiM RZQaQ RcTgT RTVYV Ri[l[ RU[X[ RTETH RVJUM RTPYPY[ RVJhJhM RbPiPj[ RlXl[ R]V`Y R_W[[ RaS^Y R]O[W RTPTVSZ RePeUdY RXSTS',
	    'PoRIYI RcJkJ RcNkN RjYlY R^U_Z RcQcZ RcEcN RfCfF RhNh[ RcEkEkN RcQlQlY RTRYW R^NZQ RaTaY R_C[L RXRTZ RaH^N R[TZZ RYKVW R`KZL RaNaRZQ RUCSR',
	    'Po^GiG RRIZI R^JiJ R^LiL R\\QkQ R\\SkS R[VlV R\\N\\S R^E^L RcNcS RgVg[ R^EjEjL R\\NkNkS RUSZX RSRVT RWWR[ RYQUZ R`V_YZ[ RUCRS RZJWW',
	    'Po\\FlF ReJkJ RaHaQ RhHhQ RSQYW RhVl[ R^JbJdN RcCeE RcS^W RfT_Z RhJkO RhIeP RXQTZ RaI^Q RVDSR RRIYJWW R\\F\\RZZ RfY_Z RjYcZ',
	    'Po[NlN R]WiW R]ZiZ R[F[I R]S][ RcCcF R]IdIcR RcScZ R]SiSi[ R[FkFkI RUSZX R^K_M RVWR[ RhJgM RdNgQlS RXQUZ RbN_QZS RUCRS RRIYIWV RhHaI',
	    'PoaHeH R\\J_J RhJjJ R\\O_P RhPjP RbQeQ RZUlU R\\F\\U R`E`U RbKbQ RbKeKeQ R`EfEfU RgEkEkU RgWk[ RTRYW R^YZ[ R_W\\Z RXPTZ R^D[F RYLVW RUCSR RRIYIWV',
	    'Po\\DkD R^HkH RRIZI R^KkK RaQiQ R\\TlT R_[h[ R\\D\\S R_V_Z RaMaQ RaFaI RgFgI RhXh[ R`NiNiQ RcUeW RURYW RVWR[ RdI`N RjVlZ RXQUY R\\VZZ RVCSR RYJWV',
	    'Po\\ElE R]HkH R\\NkN R]SjS R]UjU R]WjW R\\J\\N R]P]W RaJaN RdCdH R]PjPjW R\\JkJkN RTRZX RVVR[ RgXl[ RXQUY RaY[Z RfJgN RVCSR RRIYJWV',
	    'Po]QcQ R[TeT Rh[l[ R[E[K RhGhU RlCl[ RTRYW R\\V]Y R`MfP RXRTZ R_G_JZK R`MZP R_D[E RcDaJ RaDfDfKcK RUCSR RRIYIWU R`Q`ZZZ RdUcY\\Z RfY_Z',
	    'Po]IiI R]KkK R_QkQ R]SlS R\\VlV R]K]S R]E]I RaEaI ReEeI R]EiEiI RkDkK RUSZW RSRWT RWWR[ RdVfYk[ RYQUY RcMcQ RgMgQ R_MkMkQ R_M_Q RdScY RbY[[ RVCSR RRIZJWV',
	    'PoZFbF RdPkP RZQbQ RdSkS RdWkW RZJZQ R^C^[ RZJbJbQ RdLdW RdLkLkW RiXl[ R^QbV RSRXW ReXb[ RWRS[ R^PZX RgDeJ RcDlDkJhJ RUCRS RRIXIVV',
	    'Po[FlF RRIZI R`IgI R[N^N RaTeT RhTlT R^K^T R`C`I RaMaS RaWa[ RaMeMeS RgCgI RiKiT RlRlT RUSZX RSRWT RhWlZ RWVR[ RkMhP ReWf[ RXQUY R]W\\[ R^Q[S RUCSS RZJWV',
	    'Po\\DkD R]JaJ RfJjJ R\\MaM RfMkM RZRcR RdSlS RdWlW RdZlZ R[H[J R_O_[ RdDdN RdPd[ RdPlPl[ R[HlHlJ R^SbW RSRYW R]UZX RXRTZ R^Q\\W RUCSS RRIYIWV',
	    'PoUDjD RVJ\\J RbJhJ RUL\\L RbLiL RTRYR R\\RbR RdRjR RRUlU RSGSK RTNTR RTNYNYR R_D_L R\\NbNbR RdNdR RdNjNjR RSGkGkK R\\N]RWX RXWk[ RfUcX_YT[',
	    'Po]FdF ReFlF R^MiM R^OiO RZ[l[ R]U]Z R^J^S RaUaZ ReUeZ R]UjUjZ RTRYW R`FaH RgFjH RWVR[ RdQjT R^C\\H RYQUY RfCdH R^JiJjPfR RVCSR RRIZIWV RcS]S',
	    'PoZFlF RdLjL R[NkN R\\QjQ RZTlT R\\H\\L`L_T R\\HaHaL RcCcF RdHdL RgLgT RdHjHjL RdUgYl[ RcT[X RjVfX RVCSS[X RRI[I RZIXTS[ R^V^[bZ',
	    'Po\\HbH ReHlH R\\KbK ReKlK R^NiN R_UiU Rh[l[ R\\D\\[ R_Q_T R\\DbDcK RdLdZ ReDeK R_QiQiT ReDlDl[ RTRYX RVWR[ RdT`X RcUjX RXQTZ RVCSR RRIYIWU',
	    'PoZKlK RaOdO RZR^R RaRdR RZU]U RiZk[ R]K]Y RlWl[ RSRXW R]F_H RbFeI RiEjH R]D[J RWRS[ RcD`J RZO]OZO RaUeU RgCgQhZ RUCRS RRIXIVW RaKbYYZ RkNgVdZ[Y',
	    'Po\\GbG ReGkG R\\JbJ ReJkJ R[SlS R\\DbDbL ReDkDkL RVSZW RSQVT R\\D\\LaMbO ReDeLjMlP RWVR[ RfNdP R]N[P RYPUY RaQ_U R^Wk[ RVCSQ RRIZIWV RhUdY[[',
	    'Po\\GbG RdGlG RdJkJ R\\JbJ R\\MbM RdMkM R\\PcP RdPlP R]E]Q R`E`P ReEeQ RiEiP RTRYW R]D\\J RXQTZ ReCdJ R^SbWfYlZ R[SjSgWbY[[ RVCSR RRIYIWV',
	    'Po]GkG R^LkL R^OkO R^QlQ RaZgZ Rh[l[ R\\S\\[ R]D]G R^J^R RaWa[ RdCdG ReIeQ RaWgWgZ RkDkG R\\SlSl[ RSQYX RWVR[ RbU^V R_H[L RfTjV RYQUY RVCSR RSIZIWV',
	    'Po[FlF R]MbM ReMkM R]QlQ R]TkT R]WjW R]ZlZ R]I]M R^P^[ R`C`G R]IbIbM ReOeZ ReIeM RgCgG ReIkIkM RVSZX RSQWU RdNfP RWVR[ R_OZT RYQUY RVCTR RRIZJWV',
	    'Po[FlF RRIYI ReIkI RfRkR R^UbU RfUkU R]XbX RfXkX R`G`O R^RbQb[ RfPf[ RhGhO RSQYW R]IaIcM RgIlN RcCcF RhJcN RXQTZ R`I]O RYJVW RUCSR R[F[SZZ',
	    'Po[DbD RdDlD R[MlM R[TkT R^WcW Re[l[ R^U^[ R_H_K RaNaT RfUf[ RfNfT RhHhK R[PkPkT RSRYX RXRTZ RkVeW R\\G\\K R\\GbGbK ReGdK ReGkGkK RdZ\\[ RUCRS RRIYIWV R[M[UZZ',
	    'Po\\DaD R[FcF R\\IbI R\\KbK RRLZL R\\QbQ RRTlT RVKVR R\\M\\Q R\\MbMbQ RgKgR RXI[L RSFVI RdGfI RcLjKlL R\\SWX RjFdK RVCTG RYFSK RgDdF RYNYP RjNlQ RTNRQ ReMcQ RXWk[ RfTcX_YT[',
	    'Po\\HaH RdHjH RRIYI R[JlJ R^RbR R^UbU R^P^Y R]MbMaO R[PcPb[ RdDjDjH RhWl[ RSRXW RhWd[ RfQjY RXQTY RfOkOhX R\\D[H R\\DaDaH RdDdH RfLdT RUCSR RYJVW R[J[SZY RcX[Y',
	    'Po_IcI RRIYI RgKlK R^PlP R`WiW R^ZiZ R^M^P R`T`W RdRdY ReHeL RgMgP R`ThUhX R\\DlDlG R^MlMlP R`SlRlZ RTRXV RcO]U RXQTZ R\\GlHgI Rk[i[ RcK_K RVCSQ RYJVW R\\D\\QZX',
	    'Po[ElE RRIYI R\\KbK ReKkK ReNkN R\\NbN R^PiP R_VhV Rh[k[ R\\I\\[ R_R_V R`C`G R\\IbIbN RcOc[ ReIeN RgCgG R_RhRhV ReIkIl[ RcUgY RSQYW RcU_Z RXQTZ RVCSR RYJVW',
	    'PoRPlP RY[_[ R_K_[ RUFiFfH_K',
	    'PoUFiF RcOkO R_OiO R\\PeP RYPbP RUP_P RRQ\\Q RY[_[ R_K_[ RgG_K',
	    'Po]OlO RV[][ R]K][ RRFhF]K',
	    'PoSFiF RW[^[ R^L^[ RhG^K RTM\\QcTlU',
	    'Pob[k[ RU[Y[ RYJY[ RcDcZ RRE_EZJ RlVk[ R`NRP',
	    'PoXNeN RRTlT RZ[_[ R_Q_[ RdO_Q RiPfP RTEdEcJjIiP RZEXKTP',
	    'Po^OlO RT[X[ Ra[e[ RXJX[ ReJe[ R`EkEfJ RSE^EXK R^NRP',
	    'PoRRlR RRGRK R_C_G RRGlGlK RWLgL_P_[Z[',
	    'PoSGkG RZSlS RWMW[ R]CXMRS R]LjLgOdPd[^[',
	    'Po`[d[ RT[V[ RWKW[ RdCd[ RSE\\EWK RiJlT R`J]T R[ORR',
	    'PoWE`E RRSlS RZ[_[ R_P_[ RfFlK RVESL RVMgM_P R^G_K RgD`D',
	    'PoSFkF RRTlT RY[_[ R_C_J R_R_[ RSJkJkN RWOgO_R RSKSO',
	    'Po`IlI RbC^M R\\NRP RSE^EXKX\\T\\ RjIgQeUaY\\[ RaJbPeUj[',
	    'PoUGfG RRKlK RTUlU R[[`[ R]C]K R`S`[ RZPgP`S RjEcKRS',
	    'PoSJlJ RZP_P RZ[e[ RZTZ[ R_H_P RgTlY ReXe[ R\\RaT RVTSY RVEhE_H',
	    'PoSKkK RZQ_Q RR[l[ RVSV[ R\\S\\[ R_I_Q RbSb[ RVShSh[ RWEgE_I',
	    'PoUGeG RVMcM ReOiO RRVlV RZ[_[ R_T_[ RVJcJcN RSDiDiO RVJVN RVRgR_T',
	    'PoSE\\E R_MjM R\\MgM R^ZjZ RS[W[ RWJW[ R^Q^[ R^QjQj[ RgHkL R[FWJ RcC^M R\\ORQ',
	    'PoRF\\F R_RfR R_[k[ RS[W[ RXKX[ R`K`Z R_KfKfR RlXk[ R\\FWK R`C]L R[PRR R_GjGjTfT',
	    'PoSIlI RSUlU RZ[_[ R_S_[ RaIcLlP R^I[LRP RWPfP_S RhDUF R_E_N',
	    'PoS[W[ RWJW[ RcFcX RSE[EWJ RgUhZ R[NRP R]E]Q[Y RiD_E RgX^Y RiDiNl[',
	    'PoWPgP RRUlU RZ[_[ R_S_[ RbFcIgLlN RRF^F\\JYLSO R`EjEiHfK_N RWCTJ]M RfQ_S',
	    'PoRSlS RY[_[ RSHSM R_Q_[ RSHkHkM RUDXG RiDeH R^C_G RWMgM_P',
	    'PoeNjN RRUlU RZ[_[ R_S_[ RcJ_N RXDSI R[GSN RWPgP_S RdCcK R\\K]MSN R_GkGjN RZHSI',
	    'PoSIkI RZM_M RTO]O RaOjO R_UlU RT[X[ Ra[e[ RXRX[ R_G_M ReRe[ R\\PXR RiPeR RWEfE_G R^SRU',
	    'PoSE\\FlG RfUk[ RhKaR RdCdH^MbQ\\T RkPdW\\[ R\\EWJW[S[ R\\NRP',
	    'PoRFlF RRUlU RY[_[ R\\F\\N R_T_[ RbFbN R^CaF RfIkN RXISN RVPgP_T',
	    'PodQd[ RhTkX RgNlR R_T]X R\\NRP RSE\\EWJW[S[ RiD`F RdE^KbM RhH_QjQ',
	    'PoRElE R^UlU Ra[e[ R`G`M ReRe[ R`OiOfR RTV]Z RbHkL RXNTV RRQ]Q[VXYS[ R`FYJRK',
	    'Po^JjJ R^PjP R^UjU RS[W[ RWJW[ R^E^U R^EjEjU RgWk[ RRE\\EWJ R`X[[ R\\ORR',
	    'Po_LiL R\\PlP RS[W[ R`[d[ RWJW[ R]G]J RdCdG RdPd[ R]GkGlJ RSE\\EWJ RiSkY R^S\\X R[NRO',
	    'PoTE^E RTK^K RTN^N RRVlV RZ[_[ RTETN RYEYH RYKYN RTH^H^K R_T_[ RbHgMlO R`EjEiIeM`O RWQfQ_T',
	    'PoRF^F RTM\\M RSP]P RXU^U RT[X[ RTITM RXCXG RRVXUX[ RTI\\I\\M RlVk[ RaPfU R^ObP RaV\\[ RcCcQ_Y R\\QXS R^IiIiUiZ RcKbU',
	    'PoUHjH R[KgK RXNkN R]QaQ RaWlW RX[[[ Rc[f[ R[U[[ RfUf[ RUEjEjH RWS_S[T RbSjSfT RaMaQ RUEUQSY R`VVW',
	    'Po\\ElE R]KbK RfSkS R\\[l[ RWJW[ RbEbK R]K]SbSbZ RfSfZ RfEfKkKkS RREZFVJ RZORQ RS[V[S[',
	    'PoRFlF RSO]O R_OjO RRVlV RZ[_[ R_T_[ R[M^O RTJWL RXD[F RaJdM RhLkP RgIbO RXGUJ R[IUO ReDbF RdGaJ RWRfR_T',
	    'PoTGkG R`OlO RRO]O RcReR RVWhW RVZhZ RVSV[ RURWShSh[ RTL]LXNXR RaLjLfNeR RWDgD`F`J]J',
	    'PoaSlS Rc[f[ RUKUO R[F[[ RfQf[ RfGfK RbMjMfQ RjFlK R\\K]O RbEaL RjDbE R[F_F_U\\U RXHXPVY RYDSFSUUT',
	    'PoRElE RSP^P R`PkP RRVlV RZ[_[ RZCZH R_U_[ RdCdG R\\M^P R`KcL RhMkP RdIbK RgJbP R[JVO RXHTKXM RWRfR_T',
	    'PoTE^E RbFlF RSH_H RUNlN RWUjU R\\[`[ RYCYL R`T`[ RVKRL RYGUL RcE`H RWH^K RbFeJlL RjFfJ`L RZQgQ`T RUNUTTY',
	    'PodDjD RUGZG RUJZJ RdJiJ RRUlU RZ[_[ RSMSQ RUEUM R_T_[ RdGiFjM RSMkMkQ R`E\\G R`J\\K R]IaK R]DaF RWQfQ`S RZDTD',
	    'Po_EkE RTI\\I R^JlJ R_MlM RUO]O RRVlV RZ[_[ RTDULUO RTD\\D\\H RUK]K]O R_T_[ ReJeP ReCfE RhFgI RbGbH RWRfR_T RTHSP',
	    'Po^EkE R_JbJ RfJiJ R^MbM RfMjM R\\PlP RS[W[ Ri[k[ RWJW[ R\\H\\J R]S][ RbSb[ RdEdN RfSf[ R]SkSk[ R\\HlHlJ RSE\\EWJ RdPbU R\\NRP',
	    'PoREYE R[HlH R\\KiK R[NlN RX[l[ RR[U[ RVJV[ R\\T\\[ R`T`[ R`P`S RcCcN ReTe[ R\\EiEiK R\\TjTj[ RhPlS R\\QZS RYFUJ RePeS RYORR',
	    'Po`FkF R_JlJ RUK]K R`NlN RUP^P RSWlW R[[_[ RUHUQ RUH]H]K RTDTF^E]F RUM^M^P R_U_[ RfCfF RfJfQ RcGcJ RiGhI RYFVIRJ RWSgS_U',
	    'PoRElE R_IkI RSL\\L R^LlL R_OkO RSQ]Q RRWlW RZ[_[ RSISR RZCZG RSI\\I\\L RSN]N]Q R_V_[ RdCdG ReLeQ RbIbL RhIgL RWSfS_U',
	    'Po]GbG ReGkG R]JbJ ReJkJ ReMkM R[SlS RS[W[ RWJW[ R]DbDbM ReDeM ReDkDkM R]D]MbNbP RjNlQ R^N\\Q RgNdQ RRE[FWJ RaQ^Wk[ R[NRP RhUdY[[',
	    'Po\\DbD R[FcF R\\IbI RRKYK RdKlK R\\KbK R\\PbP RRVlV RZ[_[ RWKWQ R\\M\\P R_U_[ R\\MbMbP RhJhQ RSFUH RdFgH RjFeK RUCTG RXFSK RgCfG RXMYO RkMlP RTMRP ReMdP RWSeS_U',
	    'PoRSR[ R_K_T RRSlSl[',
	    'PoRNlN RY[_[ RSGSK R_C_G R_N_[ RSGkGkK',
	    'Poc[k[ RTGTL R_C_G RdLdZ RTGjGjL RlWk[ RXVS[ RZLYQYV',
	    'PoX[j[ RRGRK RXKXZ RRGlGlK RkWj[ R_C_G RgNWS',
	    'Pof[l[ RSGSK RTNfNf[ RSGkGkK RlWk[ R[UWYR[ R_C_G R\\J[U',
	    'Po^[j[ RSGSK R^L^[ R_C_G RSGkGkK RkWj[ RfJUM RSSlQ',
	    'PoVPhP R`[g[ RRFRJ R_C_F RVJhJhO RRFlFlJ RTTjTh[ RVOTU',
	    'PoVLhL RRRlR RY[_[ RRFRK R_C_F R_L_[ RRFlFlK',
	    'PoRNlN R^[d[ RSGSK RdJd[ RSGkGkK RVQ[V R_C_G',
	    'PoRNlN RSGSK R_C_G RSGkGkK RfNdS`W\\YU[ R]JWUk[',
	    'PoROkO RSGSK RSGkGkK RcJhL R`ObUeYl[ R]TYXR[ R_C_G R_J]T',
	    'PoRNlN RSGSK R_I_[ RSGkGkK R_OaQhWlY R_C_G R_OVXRY',
	    'PoWKgK RRQlQ RSFSK R_C_F RSFkFkK R[QYWWZR[ RcQc[k[lW',
	    'PoRNlN RSGSK R_I_N RdSl[ RZSR[ R_C_G RSGkGkK',
	    'PoSQkQ RRFRJ R_C_F RRFkFkJ R`QaUeXk[ R]TYXS[ RTLZL_L]U RhJ]K',
	    'PoRMlM R_C_G RSFkFkK ReTkZ R`P[Z RSGSK R\\IZPXURZ R`P[ZhX',
	    'PoR[l[ RSFSJ R_C_F RSFlFlJ R[QbT R[HWV RYMfMd[',
	    'PoSMkM RRSlS RRFRJ R_C_F RdHd[ RYVT[ RRFlFlJ RZHZRYW',
	    'Po`PiP R^PgP R[PcP RUQ^Q RbUkU R^UgU RZUcU RTU^U RRVZV R^[k[ RRGRK RULYL^L^[ RRGlGlJ R_C`F RlXk[ RWQSQ R`KZK RgJ\\K',
	    'PoZ[f[ RSGSK R[M[[ RSGkGkK R^JbM RgOiQlW R\\I_J RUPRW RfL^USZ R_C_G RfWf[',
	    'PoTMjM RUTiT RR[l[ RRGRK R_C_G R_I_M R_O_[ RRGlGlK',
	    'PoSLlL RZZjZ RTFTJ RZSZZ R_C_F RZSjSjZ RTFkFkJ R\\LZRSW',
	    'PoUO]O RV[[[ RRFRJ RUJ]J]P R_C^F RRFkFkJ RhRl[ RUNST]S[[ RdI`Z RfY`Z RkYcZ',
	    'PoWKgK RRPlP RZ[_[ RSFSK R_C_F R_P_[ RSFkFkK RfTkZ RXTRZ',
	    'PoWPhP RWZiZ RRFRJ RWJW[ R_C_F RWJhJhP RWTiTi[ RRFlFlJ',
	    'PoUSiS RUZiZ RRGRK RUMU[ R_C_G R_I_Z RUMiMi[ RRGlGlK',
	    'PoWLhL RaRiR RSGSK R`L`Z RSGkGkK R_C_G RXPTZ RWU\\Yf[l[',
	    'Pob[k[ RSFSJ R_C_G RbKbZ RSFkFkJ RlWl[ RaKjKiTeT RXKRR RWM^M[UXYS[ RUP[T',
	    'PoXQfQ RXVfV RR[l[ RSGSK RXLX[ RXLfLf[ RSGkGkK R_C_G',
	    'PoUMiM RVShS RR[l[ RSGSK R_M_[ RSGkGkK R_C_G RdVgY',
	    'PoRTlT RSGSK RSGkGkK RTOYQ R^C`G RXJ[L RaVl[ R_T[XXZR[ R`J_U',
	    'PoUKiK RVOiO RRTlT RSFSJ R_C_F RSFkFkJ R_TcXj[ R_I_S^V[XS[',
	    'PoTNlN Rb[k[ RTGTK RbNb[ R^C`G RYUTZ RlXk[ RbJgK R\\OXW RhQeTZY RTGkGkK R\\IZT',
	    'PoTPjP RTUjU RRGRJ RTKTW R_H_[ RTKjKjW RRGlGlJ R^C`E',
	    'PoXZgZ RSFSI RXSX[ R_C_F RUTgTg[ RSFkFkI R[HWLSN RZJ]NbQlT RZJfJbN[RRT',
	    'PoUKiK RWSgS RWXgX RR[l[ RWNWX R_C_F RWNgNgX RSFSJ RSFkFkJ',
	    'PoUJiJ RTQiQ RUUiU RR[l[ R_C_F R_R_[ RdMgOjR R\\J[NXQ RSFRJ RSFkFlJ',
	    'PoSKlK RYShS RYVhV Rd[h[ RTFTI RYNY[ R_C_F RXOhOh[ RTFkFkI R]H[LUQRR',
	    'PoUJiJ RUUhU RUZlZ RUJU[ R_C_F R_J_O R_U_Z RUOhOhU RRFlFlJ RRFRJ',
	    'PoUJiJ RYUgU RU[k[ RUJU[ RYPYU R`J`P R`U`[ RYPgPgU RRFlFlJ R^D`F RRFRJ',
	    'Pob[l[ RRFRI R_C_F RbIbZ RRFlFlI RlXl[ RcRkU RTKXM RiKcN R[UXYS[ RZRRT R\\I\\Q[V',
	    'PoRPkP RSFSJ R_C_F R_I_[ RSFlFlJ RfJeM RXJZN R_PfWlY R_PXWRY',
	    'PoWMiM RSRkR Rb[k[ RSFSJ R_H_R RbRb[ RSFkFkJ R^C_F RlXk[ RXJVO R[RYWWYR[',
	    'PoWPhP RUZjZ RRGRK RUTU[ RWKWP RWKhKhP RUTjTj[ RRGlGlK R^C_G',
	    'PoULkL RUQ_Q RXY\\Y RSFSJ R_C_F RdIhJ RaY\\[ RhOgS`Y RbQdXgZj[ RlWk[ RaLdW R`IcU RSFkFkJ RULURTY R]Q\\Y',
	    'PoRMlM RVQhQ RXZiZ RSFSI RYTY[ R_C_F RVIhIhQ RXUiUi[ RSFlFlI R^IZRWVRX',
	    'PoWPgP RUZiZ RRFRK RUTU[ RWJWP R_P^T R_C_F RWJgJgP RUTiTi[ RRFlFlK',
	    'PoVIfI RRMlM RTUkU R[[_[ RSESI R^G^M R_C_F R`S`[ RSElElI RiHdLYRRT RXQgQ_S',
	    'PoUKiK RRQlQ RTVjV RRFRI R_C_F R_H_K R_Q_[ RRFlFlI RYLZO ReLdO',
	    'Po^LlL RcPkP RcUlU RXKX[ R`C_F RcLc[ RSElElI RYHXLRQ R^M[P R`H]O RSESI',
	    'PoWOgO RRSlS RRFRJ RWJWO R_C_F R_O_[ RWJgJgO RRFlFlJ RgVkZ RdUgV RVXRZ RYUTY R\\[_[\\[',
	    'PoVJhJ RUNiN RRQlQ RWZhZ RSFSJ RWTW[ R_C_G R_H_Q RWThTh[ RSFkFkJ',
	    'PoWMgM RWPgP RRSlS RSFSI RWIWP R_C_F RWIgIgP RSFlFlI R]PWW RXWj[ RfScW_YR[',
	    'PoVQiQ RVUiU Rc[h[ RRFRJ RVMV[ R_C_F R_H_M RVMiMh[ RWIYK RgHdL RRFlFlJ',
	    'PoVJgJ R_C_F RaPcRhWlX R_OST RiNcQ RSFSJ R`JYNSO RSFkFkJ R^LaQaW_\\[\\ R`R]URZ',
	    'PoSQlQ RSFSI R_C_F RVJ[J`J_[ RSFkFkI RXLYP RfLdP R_PeVlZ R_PXWRZ RgH^J',
	    'PoVJiJ RXNhN RVRkR RSFSI RZRZZ R_C_F RSFkFkI R`SdXk[ RiTbV RVJVRTY RaYV[',
	    'PoWZfZ RSFSI RWRW[ R_C_F RWSfSf[ RSFkFkI R_LdQlT R^LXQRT RZIWMSN RcIjN',
	    'PoXMgM RRPlP Rf[l[ RSERI RXHXM R_C_F R_S_[ RgRg[ RXHgHgM RlXl[ RWWS[ RSEkEkI RXRWX',
	    'PoVZjZ RSFSJ RVUVZ R_S_Z RjUj[ RVMYO R^CaF RjLhO RYJ\\NaPiS RSFlFlJ R`PlS RVJgJcN^QRS',
	    'PoTJ\\J R_LlL RRO^O R[[k[ RRERI R[O[Z R_C_F RSEkEkH RfTiW RlXk[ RaRgU RdI`R RiMgSdU^X RVOUY',
	    'PoUJhJ RaZkZ RSFSI RWOWV R[H[L RaUaZ RcHcL RWOgOgV RSFkFkI RlVkZ R^C_F R^U\\XXYR[ R^Q^V',
	    'PoXOjO RRUlU RSFSK RXKXU RdOdU RSFlFlK R_D`F RdWk[ RZWR[ R^JXK RfI[K',
	    'Po[KlK R]TkT R]ZkZ RXLX[ R]O][ R`C`F R]OkOk[ RTFlFlI RYIXMRQ RcKbO RTFTJ',
	    'PoRRkR RSFSJ R_P_[ R_C_F RSFkFkJ ReJjO RWLTO R^K_O R`RcVlY RXJVN R^RZURY R^IXJ ReH[J',
	    'PoUJiJ RRPlP RVShS R_WjW R][l[ RSFSI R_C_F R_G_Z RVMhMhS RSFkFkI RXVS[ RYYc[',
	    'Po`KkK RXK^K RRP_P RU[Y[ RYHY[ R_C_F RSFkFkI R]S_W RUSRX RjLgUbY^[ RSFSJ RbNfVj[',
	    'PoVSiS Rb[k[ RSFSJ RWLWS R_C_F RbSbZ RUNiNiS RSFlFlJ RfUhX RlWk[ RVMRO R[HTN RYJcJ_M^UZXWZR[',
	    'PoVJiJ RRPlP RVXaX Rb[g[ RSFSI RVSVY R_C_F RVSaSaX RgPg[ RSFkFkI R^KhN R_H]LZMUO',
	    'PoWJgJ RVRhR RVVhV RSFSI RVNVV R_C_F R_J_V RVNhNhV RSFkFkI RcXl[ R[XR[',
	    'PoVZiZ RSFSJ RVTV[ R_C_F R_S_Z RiTi[ RSFkFkJ RgKlP RVLRP R\\H`J ReIaMUR RXJ^PfRgP',
	    'PoTK]K RdLkL RaOiO RRO_O R[[k[ RSFSI R[O[Z R_C_F RdIdO RSFkFkI RdShX RaRdS RlWkZ RTZR[ RhQdV_X RVOUY',
	    'PoUIiI RVLhL RROlO RVThT RVWhW Rd[h[ RVQV[ R_G_N RVQhQh[ RRFlFlI R^C`F RRFRI',
	    'PoWMjM RWQiQ RWUjU RWZlZ RSFSI RXLX[ R_C_F R`K`Z RSFkFkI RWLRQ RYHWN RaH_L',
	    'Po_JjJ RVPhP RVThT RVWhW RSFRJ RVMVW R_H_M RVMhMhW RSFlFlJ R_C_F RcYl[ R[YR[',
	    'PoUJiJ RVMhM RRPlP R^XdX RY[g[ RSFSI RYSYZ RYTeTdX RSFkFkI RhXg[ RdGdPgTlV R[G[PWTRV',
	    'PoVHiH RUVjV RUZjZ RURU[ R_R_Z RURjRj[ RRElElI RRERI R_D_E RWKgKgOWOWK',
	    'PoZPeP RR[l[ RRFRI RUSU[ R[J[O R\\S\\[ R_C_F RbSb[ RUSiSi[ RRFkFlI RfJkO ReMeO RWKSO R]GbI',
	    'Po]JkJ RTOZO RRSZS Rg[k[ RSESI RTJTP RZHZ[ R^N^[ R_C_F R^NkNk[ RSElElI RdJdQiU RaU_U RUSSZ RdKcS',
	    'PobQhQ RRUlU RSESI RWKWT R_C_E RbLbQ RUMhMhT RZGTN RYIbI_L RcWk[ R]L[QXR R[WS[ RSElElI',
	    'Po]LkL RTOZO R\\QlQ RRSZS R`[d[ RSESI RTJTP RZHZ[ R_C_F RdHd[ RSEkEkI ReSlY RbS\\Y ReQgU RdObT RUSS[',
	    'Po\\LjL R[OjO RSFSI RWMW[ RZQZT R[IjIjN RZQkQkT RSFlFlI RVMRR R`C`F RXIVO R]T`WdYk[ R\\ThTeWaYZ[',
	    'PoUJhJ RVMhM RRQlQ RSESI R_C_F RSEkEkI RdGdQgTlV R\\TcU R[G[QWURW RYXfZ',
	    'PoVLhL RVPhP RWXgX Rf[j[ RSSS[ RVIVP R_C_F R_I_W RVIhIhP RSSkSk[ RRFlFlJ RdUgX RRFRJ',
	    'PoWLgL RWOgO RRRlR R_ViV R_[l[ RSFSI RWIWO R_C_F R`R`Z RWIgIgO RSFkFkI RXVR[ R[Ye[',
	    'PoUIiI RURjR RRUlU RZ[_[ RRFRI RULUQ R\\L\\Q R`U`[ RcLcQ RULjLjQ RRFlFkI R^C`F R_I[L',
	    'PoZLkL R\\OhO RSFSH RZQZT R_C_F R\\IhIhO RZQkQkT RSFkFkI RUIXK RRNVP RWSS[ R_WdYk[ R\\ThTfWbYZ[',
	    'PoRTlT RRWlW Rd[h[ RRERI RWQW[ R[I[N R_C_F R_P_W RXNeMeN RWQhQh[ RRElElI RVJTM R\\G`I RgJkK ReH^LTO',
	    'PoWI`I RWKhK RYPhP RYRhR RYUhU RTWlW RSESH RTNTW RWGWK RYMYU R_C_F RYMhMhU RSEkEkH R[YR[ RcYl[ ReH_H',
	    'PoSESH RYKYY R_C_F RdKdY RSElElH R[L^T R[U\\YUY RgUhY`Y RiIiNkZ R^IUJUSSZ RiI`J`T_[',
	    'PoVIhI RVLhL RSOlO RWShS RRUlU RVZiZ RSESH RVWV[ R_R_Z R_I_O RiWi[ RSEkEkH RYPUT',
	    'PoVJiJ RVPhP RVShS RVVhV Rb[l[ RSFSI RVMVV R[H[M R_C_F RcVc[ RcHcM RVMhMhV RSFkFkI RlXl[ R[VZYS[',
	    'PoRUlU RZ[_[ RRFRI RUOUU R\\O\\U R`U`[ RbObU R[I[MdLdM RUOiOiU RRFkFlI R^C`F RfIjM RWJTM R]HaI',
	    'Po]KiK R\\NiN RSESI RXHX[ R[QZT R_C_F R\\HiHiN R[QkQkT RSEkEkI RSKUP RXSRV R_TbXfZl[ R]TiTfWaYZ[',
	    'PoUIhI RWPhP RWShS RRVlV RSFSJ RWLWS R[H[L R_C_F RcHcL RWLhLhS RSFkFkJ RbYl[ R_T^W[YR[',
	    'PoUTiT R[[_[ RSFSI R_C_F R_T_[ RSFkFkI RTMXO RbLjP RYHSM RVK[M ReWkZ RXXRZ RbJjJeOaQ RZRdR RXJ^JXQRS',
	    'PoRKlK RVMhM RUSjS RRUlU RS[Z[ RSESH RUPUS RVHVM R_G_S RVHhHhM RUPjPiS RSElEkH R\\SWXj[ RbYU[',
	    'PoVHhH RXMgM RXPgP RRRlR R`[d[ RSESI R_C_F RSEkEkI R^H]K RXKgKfRhVlW RXKXRWURW RXUfUd[ R]W[ZU[',
	    'Po[KlK R^NhN RTOYO RRSYS RSESI RTJTP RYHY[ R[Q[T R_C_F R^HiHiN R[QkQkT RSEkEkI R_TbXfZl[ RUSS[ R]TiTeXaZ\\[',
	    'Po]KkK R_NiN RUP[P R_RiR RSS[S R^ZjZ RRFRJ RUJUO R[I[[ R^U^[ R^UjUj[ RRFlFlJ RcHeJ R^C`E RVTTZ',
	    'Po]IiI RTOZO R\\QlQ RRSZS R^ZjZ RSFSI RTJTP RZHZ[ R^T^[ R_C_F R^MhMhQ R^TjTj[ RSFkFkI RUSS[ RbI`Q',
	    'PoSESI R_C_F RaHhHhO RSEkEkI RZMTO ReI`K RfLbN RZIUK R_NeRlS RUH]H^OWSRT R`RWT RcT]VVX RdYV[',
	    'PoRKlK RWMgM RVRhR RVUhU RVWhW RSFSI RVPVW RWHWM R_C_F R_H^M RWHhHhM RVPhPhW RSFkFkI RbYl[ R[YR[',
	    'PoZLdL RUSiS RRUlU RZ[_[ RSFSI RUNUR R[H[K R_C_F R_U_[ RUNiNiR RSFkFkI RhIkL RWISL R]GaH R[N\\S RbNbS',
	    'PoUIiI RVLhL RRPlP RSFSI R_C_F R_Q_[ RSFkFkI RdGdPhT RXUfTlU R`UcWjZ R[G[PWSRU R^U[WTZ',
	    'PoSMlM RWVhV RWZhZ RSFSJ RWRW[ R_C_F R_I_Q R_R_Z RUSiSh[ RSFkFkJ RXJYM RbOkS RfJdM R\\ORT RfHXJ',
	    'Po^IdI Rf[j[ RSFSI R^W^Z RWMfMeQ RWQiQhT RSFlFlI ReVgX RXJYL RUI[I_J_L R_CaF RgJdM RbVbY RYVX[ RVTkTj[ RWNUW RgHbH',
	    'PoWM^M RbMiM RWQiQ Re[j[ RSFSI RWJWP R_C_F RaJiJiP RSFlFlI ReVgX R[V\\Z R`VaZ RWVU[ R[QXTRV R^IWI RXTkTj[',
	    'PoUJiJ RVOhO RVRhR RVUhU Rb[l[ RSFSI RVMVU R[H[L R_C_F RcUc[ RcHcL RVMhMhU RSFlFlI ReWiX RlXl[ R[WYZR[',
	    'PoTIjI RVNhN RVQhQ RZ[e[ RVLVQ RZWZ[ R_C_F R_H_T ReYd[ RVLhLhQ RSFSGlFlH RhWl[ RVWR[ RTUgTlU R]VaW',
	    'PoVJhJ RXRfR RXUfU R[[_[ RSFSI RXOXU R_C_F R_U_[ RXOgOgU RSFkFkI RWYSZ RiKfO R_KeNlQ RdWjZ R_H^KXNRP RUKXN',
	    'PoWJiJ RWMhM RWOhO RWSkS R\\XcX Rf[j[ RSESI RTUT[ RXIXS RTU\\U\\X R_C_F R`H`S RcUcX RcUjUj[ RSElElI RXHRN',
	    'PoVKhK RRNlN RWTgT RSESH RVHVK RWPWT R\\H\\K RbHbK RWPgPgT RVHhHhK RSElElH RiUcW RZVZ[ R_TcXgZl[ RaTRX R`ZW[ RbYY[',
	    'Po^LjL R^OjO RTOZO R^QjQ RRSZS R\\TlT RSESI RTJTP RZHZ[ R^I^Q R_C_F RdQd[ R^IjIjQ RSEkEkI RcGcI RdThXlZ RcT_Y[Z RUSSZ',
	    'Po_JjJ RTN]N RTR]R ReTkT RRU^U Rd[l[ RUHUN RfPfZ RSERGkFkH R`VdZ R^C`E R_PkPjR RaLgN RXW\\Y RUOST RiKdM RXWVZR[ RaS_Z R\\JTK RXQXX',
	    'PoUH]H R`IjI RUK\\K RbNeN RVRiR RVUiU RVXiX RSESH RVPVW RYHYN ReIeN RSNYNZOiPiW RSElElH RiKkM RbK_M RbG_J R[YR[ RcYl[',
	    'PoSJ^J R`LiL RRP_P R`PiP RSS_S Rf[l[ RSESH RYHYK RYPY[ R`H`ThTg[ R`HiHiT RSEkEkH RlXl[ R[U]X RYS\\V RYSSY RULVO R\\L[O RcTaZ][',
	    'PoSJ_J RbJkJ RRN_N RbPkP RbSjS RTS]S RbUjU RTW]W RbXkX Rb[l[ RZ[][ RSESH RTPT[ RUJUN RXHXJ RTP]P][ R_C_F RcPc[ RbHbMjMkP RSEkEkH R[J[N',
	    'PoUH]H RaIiI R_KkK RUK]K RSN]N R`NjN RVRhR RVUhU RVXhX RVPVW RYHYM R_C_E ReHeN RVPhPhW RSESGlEkH R[YR[ RcYl[',
	    'PoWJ]J RaJgJ RWL]L RbLgL RWN]N RaNgN RUPiP RSRkR RVXhX RR[l[ RRFRH RUIUP RVUVX RZHZP R_H_P RdHdP RVUhUhX RiIiP RRFlFlH RZY\\[ R^D`F RdYb[',
	    'PoRIlI R][d[ ReCe[ RVN[S',
	    'Po_JlJ Rc[h[ RhCh[ RTM]V RXUR[ RaOcT R\\MWW RRG]HYT',
	    'PoUGjG RRLlL RSRlR R_[d[ R_C_L ReLe[ RXT\\X',
	    'PoVIhI RVMhM RRRlR R_[e[ ReOe[ RVEhEhM RYT]X',
	    'PoTJhJ RUNjN RRSlS R_[e[ RUEUN RePe[ RTEhEhJ RWT\\Y RkLjN',
	    'PoWF]F RRPlP R_[e[ ReLe[ RWR[W RfFlL R^G_L RVFSM RgD]E',
	    'PoRH_H R_JlJ Rc[h[ RYCYH RhCh[ RTMZS[T\\X RaOcT R\\J[QXVS[',
	    'PoTFjF RUJiJ RRNlN RRRlR R`[f[ RfNf[ R[T_X R_D\\NZSSZ',
	    'PoRE_E R`JlJ RVT\\T RRZ_Z Rd[i[ RRERY RVKVT RVK\\K\\T RiCi[ RbNeT',
	    'PoSG^G R`JlJ RRM^M RSR^R Rd[i[ RYCYM RYNYY RiDi[ RbNdT R_XRY',
	    'PoSFkF RUMiM RUQiQ RRTlT R_[e[ RUIUQ R_C_Q ReQe[ RUIiIiQ RYW[Z',
	    'PoSFkF RULiL RUOiO RRTlT R_[e[ RUIUQ R_C_Q ReRe[ RUIiIiQ RYV[Y RdDhE',
	    'Po`JlJ RUJ]J RUN]N RY[][ Rd[h[ RUFUS RUF]F][ RiCi[ RbOdT RXCXF R^OWWRY R]RRS',
	    'PoRG`G RaJlJ RdWhW R\\[k[ RYCYK RTKTQ]Q\\Z RTK_K_Q RiDiW RlWkZ RbMdR RWQVX',
	    'Po[PlP Rc[h[ RYCY[ RbGcK RhNh[ RRGVK R^TbW RkHfM R]H^L RXRRU RjC[F',
	    'PoTKYK R[QlQ RRQYQ Rc[h[ RTDTK RYCY[ RhMh[ R^TbW R_JbM RTRS[ RdCaG\\I RaFkFfK^O RaHgJ',
	    'PoSFkF RULiL RUOiO RRUlU R`[d[ RUIUO R_C_R RdSd[ RUIiIiO RWW[Y RSRkR',
	    'PoTJ`J RaJlJ RWM_M RVQ`Q Rd[i[ RX[[[ R[Q[[ RTD`D`J RiCi[ R_TaY RcOeT RWTUY RTDTRSZ',
	    'PoSGkG RbMiM RUOiO RURiR RRUlU R_[d[ RUJUR RbGbL RdRd[ RUJiJiR RYW\\Z RXD[F ReDcF R\\LXN R\\G\\L',
	    'PoVHhH RUKhK RSM]M R`RjR RRUlU R`[e[ RXMXR R`M`R RfSf[ RUEhEhK R`MjMjR RWW\\Y RSR]R',
	    'PoRF_F RaJlJ RSK^K RTS^S Rd[i[ RTNTS RYCYJ RTN^N^S RiCi[ RVTVX RbNeT R[T[X R^XRZ',
	    'PoRIaJlK RSO_O RTT^T Rd[h[ RWCWI RYOYY R[C[I RiCi[ RSDTH RUJVN RbOeT R_D]H R]J\\N R_XRZ',
	    'PoYFlF R\\KiK R\\MiM R\\PiP RRUlU R`[e[ R\\I\\P RbFaI ReRe[ R\\IiIiP RTDWG R^C_F RhCeF RWW[Y RRKWKWPRR R\\SlS',
	    'PoY[_[ R_D_[ RgKlU RWLSU',
	    'Po[[l[ R_C_U RVNSR RVXR[ RdIgLjQ RVX][ RXIUP',
	    'PoZR_R R_C_R RgOfSbW^YU[ RXGRO RfGjKlO',
	    'Po[[_[ R_L_[ RiSjW ReOiT RWSSW R_CgKlM RXOVT R_CWLRN',
	    'PoWIkI RY[_[ R_H_[ RWRRV RWHRM RfOiQkW RYCUK RXNUT',
	    'PoZ[_[ R_H_[ RjSkW ReOjS RWHRN RXRSW RWHlHiN RYCWJ RYOWS',
	    'PogPkP R[[`[ R`N`[ ReRkW RYKTO RXRSX RZFWM RSEdEdIlIkP',
	    'PoZN_N RRRlR R_C_M ReFkL RYFSL R_P\\VZXT[ R`TcXgZl[',
	    'Po_HjH RRNlN RZ[_[ R_C_[ ReRkX RXSRY',
	    'PoUTjT RR[l[ R_D_N R_P_[ ReGkM RYGSM',
	    'PoZVdV Re[j[ RTKT[ RZPZX R_C_K RZPdPdV RTKjKj[ ReEhFlJ RVHRJ RYEUI',
	    'PoYVeV Rf[l[ RRKR[ RYPYX R_C_K RYPePeV RRKlKl[ RSDXI RkDfI',
	    'PoU[Y[ Rb[e[ RYNY[ R_C_M RfNf[ RfGlL RYFSL R\\Q^V RiQlX RURRX RbR_Y',
	    'PoSNlN R[[_[ R_C_J R_R_[ ReUiZ ReEiJ RYUUY RZEUJ R^MZQTT R`NcQgSlT',
	    'PoVMhM RRRlR R^ZjZ RYZeZ RTZaZ RSISM R_C_I RSIlIlM RdTk[ RVDYG R]RTZ RhDeG',
	    'PoSFkF RXPfP RXTfT R[[_[ RXKXT R_T_[ RWLgLfT RfVj[ RYVT[ RjIfL R_C^GXL RaFfLlN RUIXKRN',
	    'PoTH\\H RTL\\L RRO_O RXT^T R\\[l[ RTDTL RTD\\D\\L RTUXY RiGlM RbG_M RiNgSdV`X ReCePcP RURS[ RYOYYbZ',
	    'PoRF_F RUJ]J RUM]M RRQ`Q RcSfS R[U_U RTY_Y RTQT[ RUCUQ R[Q[U R]C]Q RfCfS RiGlM RbH`N RjPhUeY_[ RWQVV',
	    'Po^IjI RRM\\M R^MjM R^ScS Rf[l[ RWDWM RYMYY R^D^M R^O^[ RgOg[ R^DjDjM RlXl[ RSFTK R[FYK R\\WXX RkRfT RcZ^[ RUMUSSZ',
	    'PoRJlJ Ra[k[ RaMaZ R^N[UWYR[ RlVk[ R^C^L[U R^G]S',
	    'PocZkZ RcMcZ RdEkK RlVkZ R[ESM RYVVYR[ R[MYW',
	    'PoRJlJ R`[k[ RaMaZ RcDjG RlUkZ R^D^K]P[UWYR[',
	    'PoRI^I RdWiW R[[k[ RZIZZ RbMeP RlXk[ RcD_M RaHkHjQiW RWCVMTY',
	    'Po`NgN ReWiW R\\K\\ZkYk[ R[D^J RVDSK RcD`L RbHkHkQiW RVKVRTY',
	    'PocGjG RaGhG R\\GdG RZGaG RRRlR Rc[k[ R[C^J RcRc[ RkLkO RlXk[ RZVWYR[ R[RYX RUH\\HaLdNjO RhJ`LUN RXGSH',
	    'PoRJ`J R[[k[ R[L[Z R\\D_F RlWkZ RWRTZ RkDbH RjKaO RkQiTaW RYCXLVV',
	    'Po`FlF R`MkM R_UlU RfFfU R\\K\\ZkYk[ R[C^J RVDSK RVKVSUX',
	    'Po^FkF RRI_I R_MjM R]UkU RZ[k[ RZLZZ RdFdU RlXkZ RWRSZ RXCXLVW',
	    'Po`FkF R`MkM R_UlU R\\ZkZ RSF]F\\Z RfFfU RkXlZ RVFVRSZ',
	    'PoSI^I RZ[k[ RZLZZ RgKgW ReDlK RcE]K RlWkZ RWSSZ RXCXLVW RaKaQ`U',
	    'PoUGjG RRLlL RSRlR Rc[k[ R_C_L RcRc[ ReIeR RlWl[ RYJYR R\\RYXWZS[',
	    'PoRI^I R]PkP RZ[k[ RXCYMZZ RdNdX RdPkV RdP]W RWRS[ R_M]M RjLgM R^DfDfHjGjM RbD`L RXIVW',
	    'PoSI\\I R^KkK ReShS RZ[k[ RZLZZ RbMiMiS RbMbWjVkXl[ RaFgFeJ RWCWOUX RbC_J_R^W',
	    'Po]HkH RSJ\\J R^MjM R^QjQ R^SjS R\\WkW RY[k[ RXCYOYZ R^J^Q R]EdEeW R^JjJjQ RWRSZ RXJVV RiDaE',
	    'PoRG_G R`KlK RTQ^Q RU[Y[ RTKTP RYCYG RYQY[ RTK^K^P RhEkH R]S^W RUSRY ReDeLdQaW^[ ReMg[l[lX',
	    'Po]HkH RRI\\I R\\NlN R]QjQ RY[l[ RWCXOYZ RbDbY RfEfY R]KjKjQ ReQkW RaP`S[W RVSR[ RWOTY RjDdD]E RWKUW',
	    'PoRI]I R`QhQ R`ThT ReXhX RZZkZ RYIYZ R^K^N R`N`Y R`NhNiX R`DiDiK R`D_KkKkN RkXkZ R`GdGdK RWCWOUX',
	    'PodHkH RZWlW RY[k[ RRIYIYZ R\\P\\W R]E]M RaCaM RaPaW RePeW R\\PiPiW RfJiM ReDcK RVCVNSZ',
	    'Po^HjH RSI\\I R]KlK R_OjO R_RjR R_UjU RY[k[ RWCXOYZ R^E^H R_M_T RdCdJ R^EjEjH R_MjMjT RkXlZ RfVjX RVSR[ RbV]X RWKUW',
	    'Po]DdD RfHlH R]KcK RfMkM R]NdN R\\WlW RZZkZ RREZEZY R]D]O R^Q^W R`D`G R`K`N RbQbW R]GcGcK RfQfW R^QjQjW RgDeK RUEURSZ',
	    'Po\\DdD RfHlH RRJ[J R\\KcK RfMkM R\\NdN R[WlW RX[k[ RWCXOXZ R\\D\\N R]Q]W R`D`H R`K`N RbQbW R\\GcGcK RfQfW R]QjQjW RfDeJ RWLUW',
	    'PoVLlL RVElElM RVEVQTY',
	    'PoRLlL RURhR RUEhEhT R^E^O]UYYS[',
	    'PoVNiN RWEiEiN R`NcUgYl[ RWEWPUUR[',
	    'PoUKjK RUEjEjK RUEUQSZ R^K]UXZ RXQgQg[l[mW',
	    'PoVKjK RVEjEjJ RjO[T RVEVPTY R\\M\\ZkZlV',
	    'PoVKiK RVDiDiK R[PcR RXWfZ RdKgQkV RVEVLURSW',
	    'PoUJjJ R`[k[ R`M`Z RUEjEjJ RlXk[ RUEUQTY RgKXN RkTYV RiPXR',
	    'PoUJjJ R][a[ RaKa[ RUEjEjJ RUEUQTY RXO^O\\TXY RaOdSgXlY RjNdR',
	    'PoVJjJ R[WeW Re[j[ R[R[Y R[ReReW RVEjEjJ RVNlNlTk[ RVEVOTX',
	    'PoUJjJ RZQaQ Rc[l[ RZLZZ RdLd[ RUEjEjJ RlWl[ RkOcS RaYVZ RUEUQTY',
	    'PoUJjJ RZNjN RWRlR RUEjEjJ RfUiX R`RYZaZiYj[ RUEUQTY',
	    'PoUJlJ RZMZV RZMiMiV RUDlDlI RdWl[ RbU_XY[ RUDUQTY RbQaW',
	    'PoUJkJ RaLbO RYOjOjS RUEkEkJ ReUl[ R]UW[ RdRfW R^R[X RYOYS RUEUQTY',
	    'PoUJjJ RWNlN RYZjZ RYSY[ RaJaS RYSjSj[ RUEjEjJ RUEUQTY',
	    'PoVJlJ R\\PiP R\\ViV RYZlZ RYNY[ RcLcV RVElElJ RlNl[ RVEVQTY',
	    'PoVKlK RZTlT RZYlY RZOZZ RcOcY RZOlOlZ RVElElK RVEVPTY',
	    'PoUJkJ RZRkR RYZlZ RYUY[ RZMZR RbKbZ RkMkR RUEkEkJ RlUl[ RUEUQTY',
	    'PoUJkJ RWQlQ RaVhV R[ZkZ R[L[Z RaLaV RhLhV RUEkEkJ RUEUQSZ',
	    'PoVJlJ RYTlT RYZlZ RYOY[ RcLcZ RVElElJ RYOlOl[ RVEVQTY',
	    'PoUJjJ RWMkM RXUjU RVZlZ RaRaZ RUEjEjJ R]M[R RUEUQSZ RfOjS RXRhQ',
	    'PoVJlJ RZQjQ RgZlZ RYTYZ RZMZQ RbQb[ RZMjMjQ RYTlTlZ RVElElI RVEVPTY',
	    'PoUJjJ RWMkM Rd[l[ RdMd[ RUEjEjJ RXT\\U RlXl[ RkQcT RUEUQSY R\\MZRWU RZRaR_V[YX[',
	    'PoUJjJ RWRlR RaLa[ RUDjDjJ RZM\\P RhMeP RaQgXlZ RaQZXVZ RUDUQSY',
	    'PoUJjJ RXOkO RWTlT RfOf[ RUEjEjJ RgKeO R]VYZ RUEUQTY R\\K^N^R\\X',
	    'PoUJjJ R^MlM RZQZ[ RdKdR RUEjEjI RYRUT R\\OXS R`SdXk[ RZMUO R^RjRfW^[ RUEUQSZ',
	    'PoUIjI RZSiS RZWiW Re[i[ RZPZ[ RaKaO RZPiPi[ RUEjEjI RfLkN RUEUQTY R\\L[NWO',
	    'PoUIkI RZLjL R]OiO RYSlS R]S][ RUEkEkI RiTfW RcTgXlZ RcZ]Z RUEUQTY RZLYSXY',
	    'PoUIjI RYPiP RYSiS RYWiW RYMYW RYMiMiW RUEjEjI RdXl[ R^YV[ RUEUQSZ',
	    'Po`JkJ RTL]L RdZlZ RUFUX RZLZW RdLdZ R`EkEkJ RlWkZ RjPdR R\\DTF R]WRX R`E`T^[',
	    'PoUJjJ RXNkN RWRlR R[R[Z R]K]R ReKeR RUEjEjJ RaTeXl[ RiTdW RUEUQSZ RbYWZ',
	    'PoUIkI RYMkM RXRlR RYVkV RbRb[ RUEkEkI R`KcM R]N]Q RgNeR RUEUQTY',
	    'PoaIlI RUJ`J ReWiW R[[k[ R[M[[ RUE`E`J RiDiW RkXl[ RcMeR R_MVN R_QWR RUEURSZ R`UVV',
	    'PoUJ`J RaMlM R[[k[ R[M[[ RUE`E`J RkXk[ R_MVN R_QWR RbEkEgJgWcW RUEURSZ R`UVV',
	    'PoUIjI R`UfU Re[i[ RXKX[ R`O`W R`OfOfU R_KkKj[ RUDjDjI RXL]K[Q]U]XZX RUDUQSZ',
	    'PoUIkI RXMlM Rg[k[ RYPY[ RbMb[ RUEkEkI RYPkPk[ R[V_X ReRhT RdVhX R\\S_T RUEUQTY',
	    'PoUIjI RXO`O RbOkO R`TlT RfOf[ RUEjEjI RgKeN R_SVU RUEUQSZ R[L\\O R[O[T[XW[',
	    'PoUIjI R]OlO RdUiU R`ZlZ R[P[[ R`K`[ RdJdU RiJiU R[JWN R\\NWT RUEjEjI RUEUQSY',
	    'PoUJkJ RXN_N RdNlN RdSkS RdWlW RdKd[ RUEkEkI R_K^WZ[ R^VWW RUEUQTY RXS_Q^X',
	    'PoUIlI RZRkR R[XhX Rh[l[ RXTX[ RZNZQ RbKbX RZNkNjR RUDlDlI RXTlTl[ RfViY RUDUQTY RjJ`LYL',
	    'PoUIjI RWLlL RaOgO R[RjR RWUlU R[J[R RaJaO RaRa[ RgJgO RUEjEjI RaTcWkZ RaT^WV[ RUEUQSY',
	    'PoUHjH RYLeL RWPlP R\\WiW R\\ZiZ R\\S\\[ R_J_P R[TiTi[ RdQfR RiKdOWV RUEjEjI RUEUQSY',
	    'PoUIjI RXNlN RWUlU RaJaR RUEjEjI R[K]M R_SZX RhKfM RaNjR RaNWS R[Xl[ RUEUQTY RgUcYW[',
	    'PoUHjH RWLlL RZOiO RYSiS RWUlU RZJiJiO RYSYQjQiS RUDjDjG RZJYO RaI`S[Xk[ RUDUQSY RhVbYX[',
	    'PoUIjI RdMjM R\\QkQ RdUjU Rc[l[ RZPZ[ R_L_Q ReJeZ RUEjEjI R_VcZ RZKWN R[NVS R_S]Z RUEUQSY',
	    'PoUIlI RYOlO RYRlR RZWkW RZZkZ RYLYR RZTZ[ RbLbR RZTkTk[ RYLlLlR RUElElI R\\J^L RhJeL RUEUQTY',
	    'PoUIjI R_LkL R_PjP R_SjS R`VjV RZPZ[ R_N_S R_NjNjS RUEjEjI RbS^X R[JWN R\\NWS RaJ]N R`WeYl[ RiWdY][ RUEUQSY',
	    'PoUHjH R]MkM R\\TlT RZPZ[ RdIdR RUDjDjH R_J`L RjJhL R[JWN R[NVS RdMgPlR RdMaP\\R RbS^Wk[ RiTeY\\[ RUDUQSY',
	    'PoUIjI R\\LlL RcOhO R_RkR R\\UlU RZPZ[ R_J_R RcJcO RdRd[ RhJhO RUEjEjH RZJWM R[NVS RdTgXlZ RcT_Y\\Z RUEUQSZ',
	    'PoUHjH R]MlM R_OjO R^SjS R\\VlV RZPZ[ R_J_O R_JjJjO RUDjDjH R^Q^RjQjS R[JWM R\\NVS RdIdSaV_Xk[ RiVfY][ RUDUQSY',
	    'PoUIjI R^KhK R]MlM RaShS RaYgY Rh[k[ RZPZ[ R^T^[ RaWaZ RaWgWgY R_PiOhS RUEjEjI R^TkTk[ R[JWN R\\NVT RgNlQ RdK`O\\Q RaN`S RUEUQSY',
	    'PoUGkG RXMkM R\\SgS R^[a[ RaVa[ R[QgQgV RXJXKkJjL RUDkDkG R]P\\VfWiZ R\\XY[ RjNgR RdMgQlS RYO[O R`L[QVS RUDUQSZ',
	    'PoUIjI R^KhK R]NlN RWO[O R`ShS RVT[T RaYgY Rh[k[ RWKWO R[J[[ R^U^[ R`P`S RaWaZ RaWgWgY R`PhPhS RUEjEjI R^UkUk[ RgOlQ RdK`P\\Q RUEURSZ RXTWZ',
	    'PoUHkH RXJ^J ReJkJ RXL^L ReLkL RXQlQ RZWgW RXZg[k[ R^N_Q R`S`Y RbIbM ReNeQ RZTYVfUfW RUEkEkH RXNXOkNkQ R]QXV R[SlSk[ RUEUQTY',
	    'PoUGjG RWV_V RUDjDjH RbRkRkX RiYl[ R]Y`Z RYYU[ ReYa[ R[I[NfLhK RdPiQ RbTfT R[O^OYQ R\\T_T\\T RkTgT RWR_R_X\\X RbRbXdWkWgX RbKeK RXT[TWSXX[X RaOdO R`O^OaO RUDUQSZ R_KbK`K R[K^KZJhIhOcO`O',
	    'PoRQlQ RRGRT RlGlT R_C^T[XT[ R_M^V',
	    'PoTTiT R_[k[ RTKTV R_C_[ RiKiU RkWk[ RjE\\HUI',
	    'PoRIlI RUSiS RULUU RiLiT RXDZF RgCcG R_I_V[YU[',
	    'PoRYlY RRJR[ R_D_X RlJl[',
	    'PoRHZH RTYlY RTNTY R`I`Y RlNl[ R^GXG RkE^G',
	    'PoUYjY RUSU[ R_O_Y RjSj[ R`FbJeNlQ RWD`D^IXNRQ',
	    'PoTJlJ RRPlP Rc[j[ RTETJ R`C`J RlElJ R]TYXS[ R_K]U RlQk[',
	    'PoUIjI RUMlM RUDUI R_C_I RjDjI RUMUTTY',
	    'PoTJlJ Rb[j[ RTETJ R`C`J RlElJ R^P\\VYXS[ RSOlOj[',
	    'PoRW[W Rd[j[ RRHRW RVDVW R[H[X R_R\\Z R]EhEfNlNlSj[ RaEaL_V',
	    'Po]FkF RRX[X R\\[l[ RRHRX RVDVX R[H[Y RdFd[',
	    'Po`IlI RRVZV RRIRY RVDVV RZIZX RaC]M R_NjN^Z^[k[lW',
	    'Po`NjN RRV\\V R`[k[ RRIRY RWDWV R\\I\\X R`N`[ R_EjEjO RlXk[',
	    'PoRU[U Rd[j[ RRIRX RVCVU R[I[W RbU\\[ RdP`X R`K^Q R^ElElRj[ ReGcT',
	    'Po\\FlF RRUZU Rg[l[ RRIRX RVCVU RZIZW RgHg[ R^WZ[ RlWl[ R`H`O^W',
	    'Po^EkE R]NlN RRU[U RRIRX RVCVU R[I[W RdEd[',
	    'PoRWXW R_YlY RRHRW RVCVW R_J_Y ReDeY RlJl[ R[H[WTW',
	    'Po`HlH R]ThT Rd[i[ RRIRW RVCVV RaD_OkOi[ R[I[VRV',
	    'PoSMlM RRZlZ RRPRY RSESL R_C_L R_N_Y RkEkL RlPl[',
	    'PoTJlJ RTDTJ R_C_J RlDlJ R]SaW RZR_T R\\JXORR RYOjOeU_XZYR[',
	    'PoRIjI RSSiS RS[k[ RRDRI RSSSZ R^C^I RRMiMiS RjDjI RlXk[',
	    'PoSJlJ Rc[i[ RSDSJ R`C`J RlDlJ RWQUV R`O]U[XWZR[ RRNlNj[',
	    'Po`KlK R`RlR RRWYW Rh[l[ RRHRW RVCVW R`ElElZ R`E`Q_X R[H[WTW',
	    'PoRW[W RRHRW RVDVW R[H[X R`MdUhYl[ R^LkLgTcY^[ R^F^R]Y RkD_F',
	    'Po]NkN RRHRW RVCVW RgVl[ RbU\\[ RdEdQhY RdLgW RdJdPcU RjD]F RZHZWSW',
	    'PoTIlI RgXlX RTMTX R`C`I RaMa[ RlElI RaMlMlX R^LTM RTEUI R^VRX',
	    'Po`ElE R\\OlO RRU[U Rd[h[ RRIRX RVCVU R[I[W R`E`O RhEh[ R`W\\Y RgP_X',
	    'PoRWYW Rd[i[ RRHRW RUCUW RYHYX R`P\\U RcU^[ RgLbX RaC]M RdH_S RhHeT R_IlIlPj[',
	    'PoRIjI RSOkO RRYjY RRSR[ RSDSO R^E^J R^Q^Y RRDjDjI RjSj[ RlLkO',
	    'PoRHRW RVCVW RcSj[ R`E`LeV ReNjMgTcY][ R\\EgEeM R_I]W RZHZWSW',
	    'PoUIjI RUDUH R_C_H RjDjH RVWRZ RXQTY R[Q`WeYl[ RYMVW RSLeLdQ RXQjRfVaYW[',
	    'Po]ElE R]OlO RRHRW RVCVW RhEh[ RaEaQ_W RZHZWSW',
	    'PoRWZW Rc[g[ RRHRW RVCVW RZHZW RhKlN R`W\\[ R`J[N RfEjM RbE^M RbN_X R^NiNiThZ',
	    'Po^EjE R]MlM RRUZU Rf[l[ RRIRX RVCVU RZIZW RgMg[ R`W\\[ RlWl[ RbM`X',
	    'Po\\HkH RRUZU RRIRX RVCVU RZIZW RdCdN R_NaTeXl[ R\\NjNhScW[[',
	    'PoUIiI RYOeO RUDUJ RiDiJ R`C_IaKlP RVTgTc[ R_H[LRP',
	    'Po_LiL RRU[U RRIRX RVCVU R[I[W RdDlK RcF\\L RcCaH R^QiQf[',
	    'Po_LlL RRHRW RVCVW RgLg[ RjD_F_Q]Y RZHZWTW',
	    'PoaQfQ RUZiZ RUTU[ R_R_Z RiTi[ R]EdEhHlJ R]LZOTQ RYEWHRJ RWJfJfQ',
	    'PoUIiI RUEUI RcQc[ RiEiI RYWU[ R_JbMlR R^JZNRR R_C_H_K RZQYX',
	    'Po]EkE RRXYX R][l[ RRHRX RVDVX RYHYY R]E][ RcS_X R`JdOgRiW RhHdS',
	    'PoRHlH Rf[l[ RRLR[ R_C_H RRLlLl[ RVOgW RlDkH RfNaSVX RRDRH',
	    'PoRWZW Rf[k[ RRHRW RVCVW RZHZX R_E_R RfSfZ R_EjEjR RcU]Z RlVk[ RdHdOcU',
	    'PoUF[F RRKkK RUZiZ RURUZ R_P_Z RiRi[ RbLlQ R_E]JYNRQ R^EYF RgD\\E',
	    'PoUHiH Rj[l[ RUDUH R_C_H RiDiH R^RdX R\\Q_T R\\VXX RcN[W RlWl[ RVKhKhTiZ RVKVSUY',
	    'PobMgM RRXdX Rd[j[ RRSRX R[R[X RdSdY R^C[F RYIaK RUFhFgM RUFUOlPj[',
	    'PoRJkJ RRTjT RS[k[ RRERJ RSNSZ R^N^T R^C^J RRNjNjT RjEjJ RlXk[',
	    'PoTIjI RXNlN R^QjQ R^VkV RTDTI R^M^[ R_C_I RjDjI RYKRR',
	    'Po]HlH RbMkM RbTlT RRWYW RRHRW RVDVW RYHYX RbHb[ R_C[L',
	    'Po\\QlQ RRUZU RRIRX RVCVU RZIZW R^H^Q R^HjHjQ RfVj[ RdDdRgX RbU^Z[[ RdMcU',
	    'Po^ElE R`TkT RRV[V R][l[ RRIRY RVCVV R[I[X R`E`[ R`LkLkT',
	    'PoULiL RXVfV Rf[l[ RRER[ RXPXX R_L_V RfPfX RRElEl[ RYGZK ReGcK',
	    'PoTHjH RRLlL RVVaV Rb[g[ RTDTH RVPVX R_C_H RVPaPaV RgLg[ RjDiH',
	    'Po_UgU RRU[U Re[j[ RRIRX RVCVU R[I[W R_M_V R_MgMgT RaC]M R_IlHlTj[',
	    'PoRWXW RRHRW RVCVW R]H]K R]HkHkK RgSkZ RcDeH RaR\\[ RbL^Y RfLiX R[H[WTW',
	    'PoRTZT RRHRW RVCVT RZHZV RdCdN R_NaTfYl[ R]HkHjL R]NjNhScX][ R]H]Q\\X',
	    'PoTJjJ RSPkP RR[l[ RTETI R_C_I R_K_O RjEjI RYR[Y RfRb[',
	    'PoTHlH RdQjQ RVZkZ RVTV[ R`C`H RVTkTk[ RTDTH RlDlH RSKlKjQ R_K\\OYPRR',
	    'Po_LiL R_SiS RRVZV RY[l[ RRIRY RVDVV RZIZX R_D_[ R_DiDi[',
	    'PoUIjI RSMlM RYZjZ RUEUI RYRY[ R_C_I RjEjI RXSjSj[ R[OTV',
	    'PoSGkG RTPjP RfYkY RSDSG RbHb[ RjDjG R[H[VWYS[ R_C_H RRKjKjP RRTlTkY RSPRU R[PZX',
	    'Po_QlQ RRU[U R_ZlZ RRIRY RWDWU R[I[W R_I_[ ReCeZ R_IlIl[',
	    'Po^KlK R^RlR RRU[U RRIRX RVCVU R[I[W R^D^T ReDe[ R^DlDlT',
	    'Po`LhL RRU[U RfYjY RRIRX RVCVU R[I[W RcPc[ R]PjPjX ReElL RcCbF\\L',
	    'PoRX[X Ra[k[ RRHRX RVDVW R[H[X R^H^K RaLa[ R^HkHkK RcDfF RjO`S RlWk[',
	    'Po[FlF RRXYX RZ[l[ RRHRX RVDVX RYHYX RdIdY ReKkQ RcJ[R ReFbL',
	    'PoRWYW Rg[j[ RRHRW RUDUW RYHYX R]S[W RfSb[ R_E[N RbK]T RbRcY RaM[N RbVZW RcIlIlTkZ RgCgMeW',
	    'Po`IjI R]IhI RZIdI RVZiZ RVSV[ RWGWP R_P_Z RiSi[ RlNkQ RXERK R`EcLfOjQ ReDiF R`CbK',
	    'Po^NkN RRU[U Rf[l[ RRIRX RVCVU R[I[W R^E^N RgNg[ R^EkEkN RlXl[ R`W\\[ RbN`X',
	    'PoWJkJ RRQlQ RUYjY RUSU[ R_Q_Y ReJeP RjSj[ RgDWFWQ',
	    'Po_NkN RRVZV R[[l[ RRIRY RVDVV RZIZX R_F_[ RfNf[ RjD_F',
	    'Po]JlJ RRUZU R_ZjZ RRIRX RVCVU RZIZW R_Q_[ RdCdQ R_QjQj[',
	    'Po_QlQ RRU[U R_YlY RRIRX RVCVU R[I[W R_H_[ R_HlHl[ RfCdH',
	    'Po^KkK R^PlP RRUZU RRIRX RVCVU RZIZW R^D^Z R^DkDkK RlXk[i[ ReLgY ReY[Z',
	    'PoUHjH RTLlL RYPjP RWUlU RUDUH R_C_H RaPa[ RjDjH RTLTTSY',
	    'PofNkN RRWZW R_ZlZ RRHRW RVCVW RZHZX R_Q_[ R_QlQl[ RcJ^N RdEaM R^ElEkN',
	    'PoUHiH RZOdO RcYhY RUDUJ R]S][ RUShShY RiDiI R`JlP R`C_I\\LRP',
	    'Po^MlM RRVZV R\\[g[ RRIRY RVDVV RZIZX R^F^W RlWkZiZ RjD^F RfEeLgX ReV\\X',
	    'Po]ElE R\\QlQ RRUZU RRIRX RVCVU RZIZW RdEd[ RhHkN R`H]N',
	    'PoTHjH RRNkN RTSjS RZ[_[ RTETH R_C_H R_P_[ RjEjH RhXkZ ReViX RVXRZ RXVTY R]JTS',
	    'PodJlJ RRUZU R^ZkZ RRIRX RVCVU RZIZW R^Q^[ RdCdQ R^QkQk[',
	    'PoRIlI R_SlS R]YlY RRLRV RRDRH R_C_H RlDlH R^MlMl[ RYKXWT[',
	    'PoRIlI RSSkS RSZkZ RSMS[ R_C_I R_J_Z RSMkMk[ RlDlI RRDRI',
	    'PoSHkH RgWlW RSLSV RSESH R_C_H RbLb[ RkEkH RbLlLlW R\\N\\VYYS[ R\\TRV R\\KRL',
	    'Po`GjG RRW[W RRHRW RVDVW R[H[X RaF\\J R`HgOlQ RbRgT RaC`H RiHcN\\Q R^Wj[',
	    'PoTZjZ RRJRN RTPTZ R_M_Z RjOj[ RRJlJlN RTDWH RjDeI R]D_H',
	    'Po^QkQ R\\VlV RRHRW RVCVW RdNd[ R^EcJkM R]EjEeJ]N RZHZWTW',
	    'Po^JkJ R\\JiJ R[QkQ RRUZU RRHRX RVDVU RZHZW RgLg[ RgGlK R_VZ[ RaC^J R`L`Q_W',
	    'Po^LhL RRWZW R]ZiZ RRHRW RVCVW RZHZW R]Q][ R]QiQi[ RdEkM RbF[M RcCaH',
	    'PoUHiH RYOeO RWZhZ RUDUJ RWSW[ RWShSh[ RiDiI R_C_HbLlP R_HZMRP',
	    'Po`GiG RRWYW R^ZjZ RRHRW RVDVW RYHYX R^R^[ R^RjRj[ RaE\\K R`IgOlR RbC`H RiHbO\\R',
	    'PoTHjH RWZgZ RTDTH RXSX[ R_C_H RUTgTg[ RjDjH RYLSO R\\IYL\\NaQjS RZKfKcN\\QRT',
	    'PoSJiJ RSRlR RRZlZ RRURZ RSFSR R_S_Z RSFiFiK RlUl[ R^C\\F RSNlNlR',
	    'Po^QgQ RRUZU R^VgV Re[j[ RRIRX RVCVU RZIZW R^M^X R^MgMgV R`C\\L R_HlHlUk[',
	    'Po^ElE R^MkM R^TkT RRXZX R\\[l[ RRHRX RVDVW RZHZX RdOd[ RbI^M RiIkN RcEaJ',
	    'Po[JlJ R^OiO R]SjS R\\WkW RRWYW RRHRW RUCUW RYHYX RcCc[ R^FiFiO',
	    'PoRHiH RTOgO RTSgS RTKT[ R^C^H RTKgKgS RiUbX RRERH RiDiH R]S`WdYk[ R]ZS[ R_YXZ',
	    'Po]ElE RRWYW Ri[k[ RRHRW RVCVW RYHYX R^K^[ RcJcZ RgKgZ R^KkKk[ RdFbN',
	    'Po\\HlH RRUZU RRIRX RVCVU RZIZW ReUk[ RgL`S RcI^MbQ\\T RjPdW[[ RcDcI',
	    'PoTIjI RWQgQ RVVhV RR[l[ RTETI R_C_I R_Q_[ RjEjI R[NRP R^KTP R`KfOlP',
	    'Po`IiI RaThT RRUZU Rg[l[ RRIRX RVCVU RZIZW R]E][ RaNaV RaNhNhT R]ElEl[',
	    'Po\\GlG R]OkO RRWYW RRHRW RVCVW RYHYW RdCcRfWhYl[ R]JkJjN RbU_Y[[ RkWhW R]N]RlRkW RdMbV',
	    'Po^HhH R\\MlM RRXZX Ra[k[ RRHRX RVDVW RZHZX RbQb[ RcCcM RkWk[ RkFiIaQ\\S RjRaU',
	    'PoTIjI RRNlN RVUhU RVZhZ RTDTI RVQV[ R_C_I RVQhQh[ RjDjI R_K`N',
	    'Po\\NlN RRWZW RRIRW RVDVW RZIZX RbDdF R^TkZ RcI^S RhNfUcX\\[ R\\G\\K R\\GkGkK',
	    'Po]KlK RfUjU RRUYU Rb[k[ RRIRX RVCVU RYIYW RbNb[ RaNjNjU RlXk[ R_H[L R`FgFeJ RaC^J^S\\Z',
	    'Po]FlF R^PjP R^VjV R\\[l[ RRIRW RVDVW R^J^V R^JjJjV RZIZWRW',
	    'Po]GkG R\\LlL R\\QlQ RRUZU Rb[g[ RRIRY RVCVU RZIZW RdCdL RgLg[ R_SbW',
	    'PoTIjI RRNlN RVUhU RR[l[ RTDTI R_C_I R_P_[ RjDjI RcNgRlT R]LXRRT',
	    'PoUHlH RSJbJ Rg[l[ RUDUH R`C`H ReKeW RlDlH RlIl[ RVR[T RXMRS RYJWO_O]TXYS[',
	    'PoUIjI R_ThT RS[k[ RUEUH RWSW[ R_C_H R_N_[ RjEjH R_JbMlR R_J[NRR',
	    'PoRHlH RWOgO RYWeW Rg[l[ RRLR[ RYRYX R_C_H RYReReW RRLlLl[ RlDlH RRDRH',
	    'PoTHjH RUOlO RaWfW R_C_H R[RgRgW RjDjH RYKcK`N R\\R\\ZkYk[ RTDTH R[HUOVSUY',
	    'PoTHjH RSLlL RYShS RYWhW Rc[h[ RTDTH RZNZ[ R_C_H RXPhPh[ RjDjH R]H\\LWQRS',
	    'PocGkG R\\LlL R\\PlP RRUZU RRIRX RVCVU RZIZW RcCcL RcPc[ RcSkV',
	    'Po\\HkH R[QlQ RRUZU RRIRX RVCVU RZIZW R^J_O RiJgO RdCdPbU^YZ[ RdQfVj[',
	    'Po\\EeE RRWYW Rh[l[ RRHRW RUCUW RYHYX RgGgU RlCl[ R_PbS R`V\\[ RcO_Y R`E\\Q R_JdKaV',
	    'PobGiG R^GdG R\\RlR Rf[l[ RRHRW RVCVW RgRg[ RkLkO RlXl[ RaW][ RcKfNjO RaCdL RbR`Y RiJcM]N RZHZWSW',
	    'Po\\JlJ RRWYW RRHRW RVCVW RZHZX RhQh[ ReJlP RbK]Q RdF`N R^Z\\[ RaQ`X RiD_E',
	    'Po]OlO R^TjT RTWZW R`[d[ RRHRW RVCVW RZHZX RdJd[ R]KjJjU RaFhFdJ RaF\\K',
	    'PoRFlF R_PcP RUYiY RUSU[ R_C_F R_R_Y RcFcP RiSi[ RhJlN RWISM R[LWP R\\F[M',
	    'Po]GkG RRWZW RRHRW RVDVW RZHZX RgJlN RcCdF RaK]O RaOcThYl[ RgOeT`Y\\[',
	    'Po[LlL RRUYU R\\[_[ RRIRX RVCVU RYIYW R`E`[ RiElH RcD[F RdQ[S RgDgPh[k[lW RjOhUbZ',
	    'PoTHkH RRPlP RT[Y[ RTDTH RYKY[ R_C_H RkDkH RdWh[ RfKiL RiRdW]Z RlWk[i[ RbJcV R^JSL R_TSV',
	    'PoRUZU R^ZiZ RRIRX RVDVU RZIZW R^R^[ R^RiRi[ RcIlR RfDkI RbK[R R`E\\I',
	    'Po\\NdN RRWYW R^[a[ RRHRW RUCUW RYHYX R\\EbEa[ RfEf[ RaL[Y RfElEiMlRlWhW R\\G\\N',
	    'Po]GlG R^PkP R^UkU RTW[W Rh[k[ RRHRW R[H[X R^K^[ RdCd[ R^KkKk[ RhDkE RWCWWRW',
	    'Po^PjP R^UjU RRUZU Rf[j[ RRIRX RVCVU RZIZW R^L^[ RdCdL R^LjLj[ RhFlJ R_G\\J',
	    'Po\\IlI R]NiN RRUZU R_ZjZ RRIRX RVCVU RZIZW R`Q`[ R^DiDiN R_SjSj[ R_R[V RbL^U RcG_S RcEaQ',
	    'PoTGjG RWRfR RVUhU RSXkX RTDTI R_P_[ R_C_G RjDjI R\\ISM RYKfK`N[ORQ RYK`OkQ',
	    'Po]QjQ R^TjT RRUYU R\\WlW RRIRX RUCUU RYIYW RdNd[ RbD\\J R`FdKkN R`FiFdK[O',
	    'Po\\EiE RZJlJ R[OlO R\\YjY R\\S\\[ RcPcY RjSj[ RZDSK RXNRT RW[S[ RUEWJXNXVW[ RaE`O RiFhO',
	    'PoUHkH RSPaP Rg[l[ RUDUH RZKZ[ R`C`H RdLdW RlDlH RlKl[ R\\T`U RXSRY RZR^T RZNWT R_JTL',
	    'PoUHjH R`NlN RUDUH RWMW[ R[K[X R_C_H RjDjH R`NcUgYl[ RXJTR RbJ]S RiMfTbX][',
	    'Po^IjI R^MjM R^RjR RRUZU Rf[l[ RRIRX RVCVU RZIZW R^D^R RgRg[ R^DjDjR RlXl[ RaW][ RaR`Y',
	    'Po[MlM RRUYU R^ZkZ RRIRX RVCVU RYIYW R\\G\\J R\\GkGkJ R]VZZ RiVk[ ReQaZ R`O\\Y RdCdG RbI^V',
	    'PoUIgI RUMgM RUPlP RRYcY Rc[i[ RRVRZ RZTZY RbUbY RUFgFgM R^C\\E RUFUTkTi[',
	    'PoUHiH RTKjK RRQlQ RTVjV RUDUH R_C_H R_Q_[ RiDiH R^I`K RYMZP ReLcQ',
	    'Po\\HlH R^VbV RRWYW RRHRW RUDUW RYHYX R\\NbNbU RhDjF RhTaZ RkLgU RfDfKfMhY R\\H\\R[Z RlVk[iZ',
	    'PoYIeI RSLkL RUZiZ RUUUZ R_S_Z RiUi[ ReNjP RXOSQ R_DTI R_DeGlI R_I_R[R',
	    'Po[DlD R]MkM R]RkR RRUYU RRIRX RVCVU RYIYW R]I]Q R]IkIkQ RbU_Y[[ R^TbXfZl[ RcDcQbV',
	    'PoRUYU RRIRX RVCVU RYIYW RiGlJ RaO[U R_RdXl[ R_RiRdW_ZZ[ RbC]IjI RaI_M\\O ReIeNlNlM',
	    'Po^IkI R\\NlN RRWYW R^ZjZ RRHRW RVCVW RYHYX R^R^[ RdCdN R^RjRj[ R^D\\L',
	    'Po[HlH RRUYU RRIRX RVCVU RYIYW R^NaP RhMlQ RhKfQ R_K[R RdCdQaV^YZ[ RdQfVj[',
	    'Po^JlJ RUZjZ RUTUZ R_R_Z RfJfR RjTj[ RYLSR RTDXE RRHWJ R^F]K\\P RiC]E',
	    'Po\\DkD R\\OlO RRWZW R^ZjZ RRHRW RVDVW RZHZX R^R^[ R]IhIiO R^RjRj[ RbE`O',
	    'PoRHjH RTOhO RTShS RTKT[ RTKhKhS RjDjH R^C^I_K RiTbX RRDRH R\\S`WdYl[ R\\YS[ R`YYZ',
	    'PoUHjH RRN[N RWIWW R_C_H RjDjH RcQeWhYl[ R_MkMiR R`I\\R RcQaV^YZ[ RUDUH R[URX RcNbU',
	    'Po[FlF RRWYW Rc[h[ RRHRW RUCUW RYHYX R\\L\\O R`C`I RhChI R\\LlLlO RaV^YZ[ RcOaW R\\RjRh[',
	    'Po]GkG R\\PlP RRWYW RRHRW RUCUW RYHYX RcCc[ RdQlX RcPgT RbR[X R^I`N RiIgN RcNaS',
	    'Po_MhM RRYZY R\\[l[ RRHRY RVDVX RZHZY RdFlM RdDeH RbG[N R]Q_W RjQe[ RcCaI RcPdV',
	    'Po_OlO R_TlT Rh[l[ RRHRW RVCVW RfJf[ R]KlJl[ RbF\\K RgHeL RaFiFfJ RbC_I_S]Y RZHZWTW',
	    'Po^SkS RRVZV R\\[l[ RRIRY RVDVV RZIZX R]G]J RdSd[ R]GlGkJ RgGfOkNkO R`N\\O RdDdG RbG`N',
	    'PoTGjG RWOgO RSSkS RZ[_[ RSKSN RTDTH R_C_G R_I_K R_S_[ RjDjH RSKkKkN RfVhWlZ RXUUYRZ',
	    'PoWHhH RSLkL R[R_R RTZjZ RRFRI RTUTZ R_T_Z R_L_R RjUj[ RRFlFlI R^C`F ReNkQ RXORQ',
	    'Po`OcO R^UjU RRWYW R^ZjZ RRHRW RVCVW RYHYX R^Q^[ RdCdO R^QjQj[ RfIkN RiFfI R`J\\N RdDgK R\\GaG^M',
	    'PoZJaJ RcJkJ RRWXW RRHRW RUDUW RXHXX R^C^[ RgCg[ R^LbP RfMbV RgHjU R^H[U RgHcT',
	    'PoUHjH RTKjK RROlO RTSkS RSWkW RUDUG RXKXS R_C_G R_K_[ RfKfS RjDjG',
	    'Po^JkJ R^OlO RRWYW R`ZkZ RRHRW RVCVW RYHYX R^D_S`[ ReJeT R`TkTk[ R^DkDkJ R^M\\Y',
	    'Po[GkG RRUYU RRIRX RUCUU RYIYW RcCc[ R^LaO RcPlY RhMlP RcQ[Y RhJfP R^J[Q',
	    'Po\\FkF RRUYU R]WeW Rd[i[ RRHRX RVDVU RZHZW R]Q]X R]QeQeV R[NkNj[ RcHkL RcHaJ\\L RdCbI',
	    'Po\\IlI R]ViV RRWXW R]ZiZ RRHRW RVCVW RZHZX R\\E\\O R]Q][ R]QiQi[ RlMkP RgNkP R]EdDfM RcN\\N RiCaD',
	    'Po^IjI R^MjM R^TcT RRVZV Rf[l[ RRIRY RVCVV RZIZX R^D^M R^O^[ RfOf[ R^DjDjM RlXl[ RlReU RdZ\\[',
	    'PoTGjG RUMiM RUQiQ RUU^U RX[^[ RU[\\[ RR[Y[ Ra[l[ RTDTG RUJUQ RUSU[ RbSb[ RUJiJiQ RjDjG RlYl[ R_C_G RjTaV',
	    'PoTIjI RTLkL RRVlV RTDTH R_C_H R_U_[ R_J_L RjDjH RYP]R RdN_S RZNTT RcPiS',
	    'Po_KjK RaVhV RRWYW R]ZlZ RRHRW RVDVW RYHYX R]E][ RaPaV RdGdP RaPhPhV R]ElEl[',
	    'PoTIjI RWNkN RWQjQ RWUjU RWZlZ RTDTH RXMX[ R_C_H RaLaZ RjDjH RYKRQ',
	    'Po]ElE R_JkJ R^OlO RRTZT R_TkT R]ZlZ RRHRW RVDVT RZHZV ReFeO ReQeZ R]E]Q[X',
	    'PoUHjH RUKlK RYNiN RWSkS RXVjV RV[l[ R_C_H RaLaR RaTa[ RjDjH RUDUH RUKUSSZ',
	    'PoRHlH RUPhP RXWfW Rf[l[ RRKR[ RRDRH RXSXY R_C_H R_P_W RfSfY RkDkH RRKlKl[ RYL[O ReLbO',
	    'Po_KhK R\\TjT RRUYU RRIRX RVDVU RYIYW R\\O\\[ RaOa[ RfOf[ RcCkL RbD[L R\\OjOj[h[',
	    'PoUHiH RYNeN RUViV Re[i[ RUDUI RUQU[ R\\Q\\[ RbQb[ RUQiQi[ RiDiI R`JkO R`C_H\\KRO',
	    'Po]FjF R[JkJ RRUYU RRIRX RVCVU RYIYW RcCcJ RfJfOkNkO RaP[V R_ScXk[ R`J^O[P R_SiSeW`YZ[',
	    'Po]JlJ R`RlR RRUYU R_ZlZ RRHRX RVDVU RYHYW R_T_[ R`L`R RfKfZ RlLlR R]DlDlJ RlTl[ R]D]P[Y',
	    'Po\\JlJ R[PlP R\\UkU RRXYX R][j[ RRHRX RUDUX RYHYY R_J_U R^EdEd[ RiJiU RjDaE',
	    'Po]EkE R^IjI R\\LlL R^TjT RRUZU Rf[j[ RRIRX RVCVU RZIZW R^O^[ RdOdT RdCdL R^OjOj[',
	    'Po\\FlF R_NjN R\\UlU R`[d[ RRIRX RVCVW R_I_M RcCdF RdTd[ R_IjIjM R^QjQdS RYIYWRX',
	    'PoUIiI RYPeP RUTiT RS[k[ RUDUJ R_P_[ RiDiJ ReVdZ RYVYZ R`C_IbLlP R_H[LRP',
	    'Po\\GkG R]NjN R]RjR RRUYU RRIRX RVCVU RYIYW R]J]R RcCc[ R]JjJjR RcRkY RcR\\YZY',
	    'Po]ElE RaWdW R]YlY RRHRW RVDVW R]I]Y ReIeW RlIl[ R_LbO RjLgO RgRkS RjFeI RcR^T RYHYWRW',
	    'Po[PlP R]UiU RRUYU R^[c[ RRIRX RVCVU RYIYW RcLc[ RcGcJ R]LiLiV RjFkK R^E\\K RiC^E',
	    'PoTHjH RRMlM RSTlT RTDTH R_C_H RUK_K`R RjDjH R_MePlR R_MYPRR R\\RXWk[ RhIaJ RfTbYT[',
	    'Po[LlL R`SjS RRUZU R`VjV Rf[j[ RRIRX RVCVU RZIZW R`N`[ R_PjPj[ R`O[S RbI^R RhE]J R^EiH',
	    'Po]KkK R]PkP R\\UlU RRWYW RRHRW RVDVW RYHYX R]F]P RfQf[ R]FkFkP RcO^T RdFdLbR RbCbF RdHcP',
	    'Po[GlG R]QiQ RRUYU R]UiU Rc[k[ RRIRY RUCUU RYIYW R]N]W RdKd[ R]NiNiU ReHkN RlWk[ RaH[N RbC_K',
	    'PoUHjH RRN\\N R`ZkZ R]ZhZ RUDUH RXJX[ R_C_H RjDjH RjWk[ RXP\\U RgUjX R`L^Q RbKhKjQ RXMSW RcQ`Z',
	    'Po[FlF R\\MlM R\\QiQ RcUkU RRWYW RbZlZ RRHRW RVDVW RYHYX RcCcZ R\\JiJiQ R]UbY R^S[Z',
	    'PoUHlH RUP]P RcPlP RTU]U RbUlU RY[][ Rh[l[ RUDUH RUL]L][ R`C`H RlDlH RcLlLl[ RULUSSZ RcLcTaZ',
	    'Po\\GlG R\\TlT RRWYW RRHRW RVCVW RYHYX RdQd[ R_LbO RgLkO RcCeG RhIeP R`I]P',
	    'PoUGlG RUOkO RYTkT RXZlZ RUDUG RXVX[ RYQYT RbObZ RjQjT RlVl[ RlDlG RUJkJkO R`C`G RUJURTY',
	    'PoUGiG RSKkK RVQhQ RVThT RUDUH RVNVT R_I_[ R_C_G RVNhNhT RiDiH R_TkZ R_T\\VTZ',
	    'PoTGjG RRK_K RbPlP RSQ_Q RTDTG RZNZ[ R_C_G RhPh[ RjDjG RXHTQ RkJbKbSaY RYVRV R]UWV',
	    'PoRHlH RVOhO RXWfW RRZlZ RRDRH RRKR[ RXSXW R_M_S RXSeSeW RRKlKl[ RlDlH R_C_H',
	    'Po^LhL R\\RjR RRXYX R[[k[ RRHRX RUDUW RYHYX RcLc[ RcDiJlK RbE[L R]T_Y RiTfY',
	    'Po^UlU RRW[W R^ZlZ RRHRW RVDVW R[H[X R^P^[ RePeZ R^PlPl[ R^I`M RbIeL RhIlM RkEhH ReDdG R_D]I',
	    'PoTHlH RTO_O RTR_R RTV_V Rh[l[ RTKTV RTK_K_V R`C`H RdLdW RlDlH RlKl[ R\\W_[ RWWS[ RTDTH',
	    'Po^KjK R^OjO R]RkR R^VjV RRXZX R[[l[ RRHRX RVDVX RYHYX R^G^O RdRd[ R^GjGjO RcCbF',
	    'Po^JhJ R\\RbR R\\VbV RRWYW R_[b[ Rg[j[ RRHRW RUCUW RYHYX R\\N\\[ R\\NbNb[ RfNfW RkMk[ RdDgGlI RcC_H[J',
	    'PoTGjG RTKlK RWNaN RVRbR RTDTH R_C_G RjDiG ReWi[ ReV`[ RlWk[ RXV_Y ReIhJ RhNdX R[NWW R_R]W[YV[ RcJeV RTKTTSZ',
	    'Po_HjH R_LjL R\\OlO RRWYW Rf[j[ RRHRW RVCVW RYHYW R_D_L R_DjDjL R`Q\\U RbU]Z RgRcZ RbR`W R`O^SkRj[',
	    'PoTHiH RSM^M R`NlN RRQ_Q RSU^U Rd[h[ RTDTH RXJXQ RXRXZ R_C_H RhJh[ RiDiH RbRdV R_YTZ',
	    'Po\\HlH RfRkR R]VkV RRWYW R]ZkZ RRHRW RVDVW RYHYX R]M][ RbHbQ RgGgR R]MkMk[ R`DaG RhDfI',
	    'Po]ElE R^MjM R^RjR R^VjV RRW[W RRHRW RVCVW R[H[X R^I^V R^IjIjV RfXl[ RdEcI RaX[[',
	    'Po\\FlF RRUYU RgYjY RRIRX RVCVU RYIYW R\\L\\O R^Q^X RdCdF RdNd[ R^QkQkX R\\LlLlO R`G`K RhGgJ',
	    'PoTHjH R_LlL RRL\\L RW[Z[ Ra[k[ RTDTH RVQ[Q[Z R_C_H RaNa[ ReMeW RjDjH RWIXK RkXl[ R`I^O RkPkVhV RkP]R RVMTY',
	    'PobGkG RaLlL RaQlQ RRWXW Re[h[ RRIRW RUDUW RXIXX R_K_[ RfCfL RhNh[ RcTdW R_C^G[I R^LZP R_I\\O',
	    'Po^HjH R^LjL R\\PlP RdUjU RRWYW RcZlZ RRHRW RVCVW RYHYX R^E^L RdPdZ R^EjEjL R^VbZ R^T[[',
	    'PoTHjH RUOiO RUSiS RY[e[ RTDTH RUKUR RZUZ[ R_C_H R_K_R RUKiKiR RjDjH RgUlZ ReXe[ R]TaV RUUSZ',
	    'Po\\ElE R\\NkN R\\RlR RRWYW RRHRW RVCVW RYHYX R\\H\\N RaEaN RfEfN R\\HkHkN RbP`S^VkZ RhTdY[[',
	    'Po]JbJ ReJkJ R^MjM R[QlQ RRUYU Rc[h[ RRIRX RVCVU RYIYW R]D]J R]DbDbJ ReDeJ ReDkDkJ R`Q_UjTh[',
	    'PoUHjH R^NkN RRN\\N R_TjT R\\[l[ RUDUH RXJX[ R_C_H ReMeZ RjDjH RXO\\T RcJdN RUTRW RXNTV',
	    'Po[GlG R_JhJ R_MhM RZQlQ RRUZU R]ZlZ RRIRX RVCVU RZIZW R]Q][ R_C_Q RhCgQfVkUkV R`V^W RbQaV',
	    'PoUHjH RZNlN R[RjR R[TkT RYXlX RUDUH RWPW[ R_C_H RcIc[ R[KjKjQ RjDjH RXJSN RXNRT',
	    'Po]FlF R\\MlM RRUYU RRIRX RVDVU RYIYW RdCdF R`HiK RgI^K RgP_R RiReU`V RkVfY^[ R\\M\\S[Z',
	    'Po\\GkG R]KjK R[OkO R[SlS RRUZU RRIRX RVCVU RZIZW RdGdO R_C`F RhCgF RcReWhYl[ RcV`Y[[ RdPbX',
	    'PoTHjH RULlL RWO_O RXW_W RXRXX RXR_R_W R_C_H RjEjH RdWi[ RbQfY RiOhRbY][ RTEUH RdJiK RlXk[i[ RaJdW RULUTSZ',
	    'Po`HlH RRI_I RUYiY RUSU[ RZEZQ R_R_Y ReCeO RiSi[ RePlOlP RiDkF RdK_P RYIRP RYK^M R^DTE',
	    'Po\\GlG RRK\\K RdOlO R]YkY RYEY[ R]S][ RdQdY ReIeO RkSk[ RlMlO RXN\\R RbK^P RfDjE RXKST RcEbL R\\DRE',
	    'Po^KjK R\\OlO RRUYU RRIRX RVCVU RYIYW RcFcJ RhFkI R`SdXl[ R^E[K R`Q\\Z R`SjSeX^[ RaM^X RiC^E',
	    'Po\\FlF R_MiM R^SjS RRWZW R`[d[ RRHRW RVDVW RZHZX R\\P\\S R_I_M RdSd[ R_IiIiM R\\PlPlS RcCdF',
	    'Po[IlI RfRkR RRUYU RRIRX RUCUU RYIYW R\\EcEd[ RgJgR RcRiXlY RkMfN RcR]XZY R[M`L`R[Q RiD_E',
	    'Po]JlJ R`RlR RRUZU R`VlV R`ZlZ RRHRX RVDVT RZHZW R`M`[ ReDeJ R`MlMl[ R]DlDlJ R]D]R\\Y',
	    'PoTHjH RRM_M RUS]S RUY]Y RTDTH RUJU[ R]J]Z R_C_H RjDjH RbMkMjQ RbI`R RfOfSdW_[ RfTgWl[',
	    'PoTD^D RVRZR RUZiZ RUTUZ RZIZQ R_R_Z RiTi[ R]EYG RZITO RRI_I]M RVF\\I RcGeLhPlQ RdC`K RcGkGhLeO_Q',
	    'Po]IkI R]MkM RRUZU R`ViV Rg[l[ RRIRX RVCVU RZIZW R\\Q\\[ R]D]M RdDdV R]DkDkM R\\QlQl[ RhSjX',
	    'PoUGjG RVOhO RYUfU RUDUG RVIVN R_C_G RhIhN RjDjG RcPkS R[PTS R\\SUW RZIdL RcI^KXM RZV`Yl[ ReV`X\\ZR[',
	    'PoUGiG RYUeU RUDUG RYQYU R_C_G RYQeQeU RiDiG RcWg[ RUJUSTY RdL^NYN RWZeY R_N_Y RUJhJhUj[l[mX',
	    'Po^HjH R^LjL R_PlP RRUZU R^XiX Rg[k[ RRIRX RVCVU RZIZW R^R^Y R^D^L R^DjDjL R`N[S RdSaV RdTgU RlQk[',
	    'PoWIgI RRQ\\Q RaQlQ RTZjZ RRLRQ RTUT[ RWDWI RRL\\L\\Q R_S_Z RaLaQ RWDgDgI RjUj[ RaLlLlQ',
	    'PoSHkH RWQgQ RRZ\\Z RaZlZ RRTR[ RSDSH RWKWQ RRT\\T\\[ R_C_H RaTa[ RWKgKgQ RkDkH RaTlTl[',
	    'PoTHjH RUNhN RUQhQ RRSlS RTDTH RUKUQ R_C_H R_K_Q RUKhKhQ RjDjH RiUcX R^SbXfZl[ RXSYZT[ R_YXZ',
	    'Po]LjL RRUYU RRIRX RUCUU RYIYW R]D]L RjDjL ReLfPkOkP RbP\\V RgDaJ R_TcXj[ R`FgI RaL`P\\Q R`SiSeWaY[[',
	    'Po\\GlG R^SiS R^WiW RRXYX R\\[l[ RRHRX RVDVX RYHYY R^O^W RdDdM R^OiOiW RdGgKlM RdG_K\\M',
	    'PoUHjH RUMlM RWQ`Q R_C_H RjEjH ReWi[ R`TZY RhSaZ RcRfY RfKiL RhPfV RYSWW R\\Q\\XV[ RUEUI RlWk[i[ RbKdW RUMUTSZ',
	    'Po[FlF R\\PkP RRWYW RRHRW RVCVW RYHYX R\\J\\N R`C`I RcLc[ RgCgI R\\JkJkN ReRlX RbPaS[Y',
	    'PoUHjH RRP^P RUDUH RYJY[ R_C_H RhIh[ RjDjH RaPdS RWP]U RbKeN RVTRX RXNUU R]JRK RjU^W',
	    'PoTHjH RUL]L RTP\\P RST\\T RTDTH RXPXZ R_C_H RjDjH RfVk[ RcV_[ ReRbY RaMkMjR RVISO R\\XV[ RbI_R ReNfV',
	    'PoTHjH RZNdN RVT^T RVW^W Re[i[ R[[^[ RVQV[ RVQ^Q^[ RcQcX RiPi[ R`C`I\\KSO RTDTH R_JfMlN RjDjH',
	    'PoTGjG RTKjK RRPlP RVXaX Ra[g[ RTDTG RVSVZ R_C_K RVSaSaX RgPg[ RjDjG RYLZO ReLdO',
	    'Po\\IlI R\\SlS RRWYW RRHRW RVCVW RYHYX RdCdO R^D`H RiDhG RdHgLlN RdH`L\\N R^Wk[ RbP_U_V RgUdY\\[',
	    'PoSLkL RSY]Y R`ZkZ RSTSY RXRXY R]T][ R`T`Z ReReZ RkTk[ RdNjP R_DiH RXNSQ R_DTI RYHcGlI R_H_Q[Q',
	    'PoTHjH RTKlK RYPkP RYSiS RYViV RYZlZ RZOZ[ R_C_H RaNaZ RbLaO RZNVS RTDTH RjDjH RTKTTSZ',
	    'Po\\RkR R[UlU RRUYU RRIRX RUCUU RYIYW RcFdI RhOkR RhFlI RbI_M RfKaP R^LcN RdVgYl[ R^E\\K RcSbW`Y[[ RiC^E',
	    'Po\\IdI RWOfO RX[\\[ Re[i[ RSR\\R\\[ R_C_G R_RiRj[ RTDTGjEiG R]KYO ReJlM RbLfN RZRRU RYWRZ RgR_U RfV_Z RYJRM',
	    'PoaIgI R^OiO R^RiR RRUYU R_ZjZ RRIRX RVCVU RYIYW R^L^U_[ R^LjLjR R_UjUj[ RdCiIlJ RcC]J R^O]Y',
	    'Po]FlF R]MlM R_QjQ RRWYW R_ZjZ RRHRW RVCVW RYHYX R_T_[ RdHdT R_JjJjQ R_TjTj[ RcCfF R]F]R[Z',
	    'PoUZiZ RSNSQ RUSU[ R_P_Z RiSi[ RSNjNjR RhKlL RiEhH R]E\\H RaE`H RXH]J RdHjK RUETH RcJ^M RYHVKRM RYDWJ ReDcJ',
	    'PodGlG RdMlM R[SlS RRWXW RRHRW RUDUW RXHXX RcPc[ RdIdM RiDiP RgWlY RcRjY R^XZZ RcR\\Y R^JaK\\P R^D\\J R]GbG_M',
	    'Po]IjI R[MlM R^UiU RRXYX R[[l[ RRHRX RVDVW RYHYX R]E]I R^P^U RcCcI R^PiPiU RjEjI R`W`Z RgVe[',
	    'Po]UkU RRWYW R]ZkZ RRHRW RVDVW RYHYX R]E]N R]P][ RdPdZ R]PkPk[ RdElElM RaHcK\\M RkNhN RbD\\E RgEfM',
	    'Po]KiK R]OlO RRWZW Re[j[ RRHRW RVDVW RZHZX R`V`Y R]FiFiK RcUdX RfUiW RcCbF R]V[Z R]F]SkSj[',
	    'PoSGkG RRJlJ RWPhP RYYfY Rf[k[ RSSS[ RSDSG RWMWP RYVYZ R_H_J RWMhMhP RkDkG RSSkSk[ RYVeVfY R_C_G',
	    'Po\\FlF R_MiM RRUZU RaXhX Rh[l[ RRIRX RVDVU RZIZW R]P][ R_I_M RaSaY RdCdF RaShShX R_IiIiM R]PlPl[',
	    'Po[IlI RRXYX RRHRX RVDVW RYHYX R^V\\Z R_DaG RgJdQ RjMdZ R_J\\Q RbN^W RhCfH RiQdQ RaUbZ[Z RjUkZdZ RaQ\\Q',
	    'PoTGjG RUOiO RUSiS RcXjX RTDTH RUKUS R_K_S RUKiKiS RjDjH RaSa[kZl[ RhViY ReTdX R[SYWWYR[ R_D^K',
	    'Po[FlF R[PlP RRUYU Rd[i[ RRIRX RVCVU RYIYW R[K[N RcCcF RcMcP R[KlKlN R`W\\[ R`G`K RhGgJ RaP_X RaTjTi[',
	    'Po]FkF R^LiL R^OiO R^SiS R\\VlV RRWYW RRHRW RVCVW RYHYX R^I^V R^IiIiV RhXk[ R`Y[[ RdCbI',
	    'Po\\GkG R]KjK R[PlP R^TkT RRVYV R\\[l[ RRIRY RVDVV RYIYX RcGcP RdTd[ R_C`F RhCgF R`P^UZ[',
	    'Po[HlH R[OlO R\\SjS RRUYU RRIRX RVCVU RYIYW RaDa[ RfEf[ R\\KjKjS ReSkZ RaSZZ RiDdD]E',
	    'PoRUYU R^ZjZ RRIRX RVDVU RYIYW R\\G\\J R^S^[ R^SjSj[ R\\GkGlJ RfIjM RcM]S RaI\\N RdMiQlS RdCdG',
	    'Po^IjI R^LjL R^OjO R\\SlS RRWZW RRHRW RVDVW RZHZX R^F^O RdOd[ R^FjFjO RdSjY RdR]Y RcCcF',
	    'PoUHjH RTKkK RUOjO RSSlS R[VjV RX[l[ RUDUG R_C_G R_K_S RbVb[ RjDjG RYIZK RZSSZ ReHdK',
	    'Po^RjR R^VjV RRWYW Rf[j[ RRHRW RVCVW RYHYX R^N^[ R^NjNj[ RcHhLlN R]EaF RgIkJ RaI\\J RjDfF RbJ`M[N RdDbK',
	    'PoRGjG R\\LkL R[PlP RRQYQ R[TlT RRXYX Rc[g[ RRJRZ RRDSH RRJYJYX R^C^G RcIcP RgPg[ RjDjG R^VaY',
	    'Po\\JbJ ReJlJ RRUYU R][a[ Rf[k[ RRHRX RUDUU RYHYV R\\DbDbJ ReDlDlJ ReOlOlZ R_Q[S RaN]S RhRdS RkNfS RbR^WYY RlRgWcX ReJeO R\\J\\ObNbVa[',
	    'PofRkR RRWXW RRHRW RUCUW RXHXX R]L][ RcVfZ RcGeJ RbSdY RiFkL R^C\\I R^J[O RcDaL RiDfL RgNfZl[ RbO`Y',
	    'PoTHjH RcUjU Rb[l[ RWPW[ R_C_H RcQcZ RjDjH R^LaO RgLkP RXISN RYMRT R^MZQ R_J\\O R]SZZ RgJeO RTDTH R]V`Zg[',
	    'PoSGjG R]MkM RSNYN R]UjU RbZkZ R_ZiZ R\\ZgZ RT[X[ RSDSG RRJYJYO R]P]U RdMdZ RjDjG R]PjPjU RhWlZ RgJkN RaI_M R_C_G RSNRTYSX[',
	    'PoRUYU RRIRX RUCUU RYIYW R[DbDbL RdDjDjL RhHeL R_E[H R`I\\M RgEdH RdNlR RbM`OZR RdQ]T RfS^W RhVcY\\[',
	    'Po]FlF R_MlM R`QjQ RRWYW Ra[d[ RRHRW RVDVW RYHYX RdEd[ R`JjJjQ R_SaU RjSfV RdTiXlY RdV^Y R]F]S[Z',
	    'Po\\ElE R]KlK R`QkQ R`UjU RRWYW R^ZlZ RRHRW RVCVW RYHYX ReMeZ RaGbJ RcCdE RiGgK RaM_S R]K]S[Z',
	    'Po[GlG RaKeK RiLlL RRWXW RfYiY RRHRW RUCUW RXHXX R\\N\\Q R^R^Y RaCaK RcOc[ ReCeK RiCiK R^RiRiY R\\NkNkQ R^D]K',
	    'PoTGjG RTKhK RSP\\P RdPkP RTDTG R_C_G RjDjG RiNlP R]GYJ R`NkT RcHiK RVLUP RfLdP R_N]ORT R^SVU RcTWX RcYU[',
	    'PoTGjG R]JlJ RRK\\K R^QjQ R^TjT R^WjW RS[W[ RTDTG RWKWZ R^M^W R_C_G RdJcM RjDjG R^MjMjW RgYl[ RaY[[',
	    'Po\\DkD R^JaJ RfJiJ R\\MaM RfMjM R]PjP R[SlS RRWYW Rc[h[ RRHRW RUDUW RYHYX R[G[J RcDcN R[GlGlJ R_S^ViVh[',
	    'Po[HlH R]KiK R]QjQ R[TlT RRUYU RRIRX RVCVU RYIYW R]N]Q R]E]K R]EiEiK R]NjNjQ RcCcQ`T^Wk[ RiTdY\\[',
	    'Po\\EkE R[JlJ R]PjP R]SjS RRUYU R[VlV RRIRX RVCVU RYIYW R]L]S RdSd[ R]LjLjS R_EaI RhEfI',
	    'Po[FcF R[NbN R[SbS RRUXU RZVcV RRHRX RUCUU RYHYW R[J[S R_C_[ R[JbJbS ReLkLj[ RkDeEeScZ',
	    'PoTGjG RSK_K RbQlQ RSQ^Q RST^T RRW_W RSNST RTDTH RYIY[ RSN^N^T R_C_G RhQh[ RjDjH RbKbS`Y RjJaK',
	    'PoSF^F RTH]H RRK_K RUZiZ RUUUZ R_T_Z RiUi[ RVORS RbHdLgPlR RYCXKUQ RcCaJ RbHkGgMdP`R RVN]N\\RYS',
	    'Po\\FlF R\\MkM R\\QkQ R]WjW RRWYW R]ZjZ RRHRW RVCVW RYHYX R\\I\\P R]S][ RaCaP RfCfP R]SjSj[ R\\IkIkP',
	    'Po\\ElE RaMiM RRUYU R_WdW RfWkW R\\ZlZ RRIRX RUCUU RYIYW R\\E\\[ R_Q_V RaHaM R_QdQdV RfQfV RaHiHiM RfQkQkV',
	    'PoTGjG RTOYO RRSYS R\\SlS Rc[g[ RTJTO RTDTG RYIY[ R_C_G RhPh[ RjDjG R_UbX R_LbP RcJ\\M RaKcLjKgMcP\\Q RUSSZ',
	    'PoSHjH R]NlN R^QjQ R^UkU RRWYW R^YlY RRMRY RSESI RRMYMYW R^L^[ R^C^H RfLfY RdKfM R_J\\P RiEiI',
	    'PoTHjH RRM[M R^NlN R^QkQ R^VkV R^ZlZ RT[W[ RTDTH RWIW[ R_L_[ R_C_H ReLeZ RjDjH R_J\\Q RfJeN RZQRS',
	    'Po^IiI R^MiM R^PlP RRUZU Re[j[ RRIRX RVCVU RZIZW R`V`Y R^FiFiL RcUdY RfUhW RcCcF R\\VZZ R^F^RkRj[',
	    'PoTGjG RVMhM RVPhP RVRlR Rd[i[ RTDTG RYWZ[ RVJhJhO RjDjG RdWfY R_W`Z RUWSZ R_E]J RVJVUjUi[',
	    'Po^UkU RRWYW R^ZkZ RRHRW RVCVW RYHYX R^Q^[ R\\DcDcM RdNcQ R^QkQk[ ReDlDlM R]G`H RfGiH RcJ\\M RlIeL',
	    'PoUGjG RaNlN RTN_N RWQ_Q RVT`T Rd[h[ RX[[[ RUDUG R[T[[ R_C_G RTJ_J_N RiIi[ RjDjG R^V_Z RcQeV RWVUZ RTJTSS[',
	    'PoTHjH RTKlK RWMaM RcSjS R]TaT RWWaW RWZaZ Rd[l[ RWPW[ R[M[T R^M^T R_C_H RWPaPa[ RdSdZ RjDjH RcMjMkS RlXl[ RTDTH RTKTTSZ',
	    'PoRGjG R_KhK R^QhQ R[UjU R\\WiW RY[k[ RRJR[ RRDRG R^C^G RbQb[ RjDjG RWQXU R]SZV RRJXJUP R`JYM R^KcNlP RgLbNYQ RXWTW',
	    'Po^MkM RRWYW RRHRW RVCVW RYHYW R^H^M RfSfZ R\\IkIkM RaD[J RhRkY RdLbO\\R RdR\\U RjQeScV[Y RdQfW R`EhEeHdN Re[a[',
	    'Po]PiP RRUYU R]WiW R]ZiZ RRIRX RUCUU RYIYW R]H]P R]S][ RcJcP R]SiSi[ R\\JjIjP RhFlJ R`EgDiH R^GZK R`K_N RfKeN',
	    'Po\\JlJ RRUYU R]VjV R]ZjZ RRIRX RUCUU RYIYW R]R][ RdDdP RdRdZ R]RjRj[ R^F_I RdIiOlP RiFgI RcJ^O[P RiC^E',
	    'PoTHjH RUN\\N RSR]R RTDTH RXNXZ R_C_H RjDjH RfVk[ RYI]N RcV_[ RXIRO RUTUX ReRhY ReRbY RaNkMjR R\\T[X RbI_Q ReNdV R^YR[',
	    'Po^IlI R^LkL R^PkP R^TlT RRUZU RRIRX RVCVU RZIZW R_G_T RaWa[ ReFeT RiVl[ R`C\\K ReDdJ R]V[[ ReVf[',
	    'Po_HgH R^OiO RRWXW R^[b[ Rg[k[ RRHRW RUDUW RXHXX R^K^O R[QbQb[ R^KiKiO RdQkQk[ R\\S_U ReShT RcDlH RbVZY RkVcY RcCaF[H',
	    'PoRUYU Rd[h[ RRIRX RVCVU RYIYW R\\N\\Q R\\NkNkQ R_HbK RhHlL RkEjH R]E]H RbEbH ReEeH R^K\\L RfKdL RbV_Z[[ RcPaX R`C_J RhCgJ R\\SjSi[',
	    'PoTHjH RaMjM R^MfM RRN\\N RaUiU R_UfU RWJW[ R_C_H RjDjH RWO[T RkPlR RdRgS RaRcYi[ RdJgK RWMSW RhV_Z RTDUH R`IbMePjR RiMdP]R R\\UbUfY[[ RlYl[i[',
	    'Po\\IlI RdQkQ RRUYU RbWlW RRIRX RVCVU RYIYW RdCdN RdQdW RiOi[ R^D`F RjDgG R\\S`V[[ RfJlM R^O\\T R]RbQ_X RdH`L\\M',
	    'PoUHjH RSKlK RXRfR RXVfV R[[_[ RUDUH RXOXU R_C_H R_V_[ RXPfOfU RjDjH RXWUZ RiLfP R]JWP RaKeOlQ ReXiZ RULYORR',
	    'Po[FlF R^PiP R^TiT RRUZU R`[c[ RRIRX RVCVU RZIZW R_L_S RcTc[ R]MjLiS RhWkZ R]I_K ReHlN RjHhK R_V[Z RaI[N RcC`K',
	    'PoTGjG RSJ^J R`JkJ RSN^N R`NlN RgQlQ RVWiW RVZiZ RTKSM RTDTH RVSV[ R_C_G R`K`M RhMhQ RVSiSi[ RjDjH RZMYR RYJXOUQRR RfJeNcQ_QXQ',
	    'Po_LgL R]TiT RRVYV RZ[k[ RRIRY RUDUV RYIYX R]O]T R]OiOiT RfHkN RiDfG RkHhJ R\\G_I[N R_V`Z RdDgJ R\\EaE_K RgVfZ',
	    'Po[ElE RaGkG R^JlJ R^NjN R^QjQ RfSjS RTZjZ RRDRS RTVTZ R^L^S R^U^Z ReHfJ RjVj[ R^LjLjS RVJYM R`E[J RREYEVJ RXPUP',
	    'PoeNjN R]PjP R]SjS R[UlU RRUYU Rc[g[ RRIRX RUCUU RYIYW R]J]S R_GgGfM RgSg[ R]JjJjS R^W`Y RbEgDlI RaL_N R_E[I RbGaM',
	    'Po\\EdE R\\HlH R_OhO RRUYU R`XgX Rh[k[ RRIRX RVDVU RYIYW R\\R\\[ R`J`O R`T`Y R`TgTgX R^KiKhO R\\RkRk[ ReHhKlL RcF^K[L RiCaD',
	    'PoTGjG RTJkJ RVOaO RWV_V RTDTG RWQWV R_C_G R`Q`V RjDjG RcOkOjS RhWl[ RXKYN R[O[WW[ ReVa[ R_L\\O RfSiY RdKbS RTJTSSZ RfOeW',
	    'Po]FjF R[IkI R[MbM RdMkM RcQlQ R[TlT RRUYU Rf[l[ RRIRX RVCVU RYIYW R_K_Q RcCcI RfTf[ RgKgQ RlXl[ R`T^YY[ RbP[Q',
	    'PoTGkG RWKkK RVNjN RVRjR RVUkU RTDTG RWKWV R[W\\[ R`I`U RjDjG RgWl[ RVWS[ RXIRO RaWc[ R_C_G',
	    'PoTGjG RUIjI RSLlL RSO]O R`OkO RVR]R R_RlR RSSZS RRUlU Rc[l[ RTDTG RXLXR R_C_L RcUc[ ReLeR RjDjG R[VXZR[',
	    'Po\\ElE R^KeK R^TdT RRWYW RRGRW RVCVW RYGYX R^N^T RaKaW RdNdT R_G_J RcGaN R_Z]Z RgLkLkP RgGfQ RhNgX RhOjY R\\E\\T[[',
	    'Po[IlI R^OlO R_QjQ R_TjT RRWYW R_ZkZ RRHRW RVCVW RYHYX R_V_[ R_VkVk[ ReLeO R^EfEdH RbJ_L RgJlL R_D\\H\\S[[',
	    'Po\\IkI R]LjL R[OlO R\\TlT RRUYU R\\WkW RRIRX RVCVU RYIYW R\\D\\I RaEaI RcIcO RcTc[ RfEfI R\\DkDkI RgPgS R`Q`T',
	    'PoTHZH RUKZK RdKjK RUZjZ RUTUZ RUEUO R_R_Z RdHjGjO RjTj[ RdDjDjM RROlOlS RaD^G R]JcL R\\DbG RaK\\M RYDTD RRORR',
	    'Po]HjH R]LjL R\\NkN R_QjQ R\\TkT RRWYW RRHRW RVCVW RYHYW R]E]L R`T`[ RbCbL RfCfL R]EjEjL RdUhYl[ RjUfW RdZ^Z R\\N\\U[[',
	    'PoSGjG RRMlM RTPjP RUSiS RRVlV RSDSG R\\H\\L R_P_[ R_C_G RaHaL RjDjG RWIXL RgJdL RdNaP RZN[P R_VT[ R_VfYk[',
	    'Po[HlH R\\MkM R]QjQ R[TlT RRUYU RRIRX RVCVU RYIYW RaCaH RcMc[ ReCeH R\\D^F RjDgG RgIfL R`J`M RcTgXlZ RcT_XZZ',
	    'Po\\FkF R]JjJ R[MlM R[SlS RRUYU R\\[_[ RRIRX RVCVU RYIYW R_O_[ RcFcM RlYk[ RgXj[ RhOkP R^D`E RhDfE RjUfYbZ ReOgY RcO[P RdV[W',
	    'PoRHjH R^KkK RRQ[Q R]QlQ R^VkV RRY[Y RRKR[ RRDSH RRK[K[P RRT[T\\Z R^C^G RdQd[ RjDjG RcIfK RVIVL R`MaP RhMfQ',
	    'Po\\SaS ReSjS RRUYU RRHRX RUDUU RYHYW R\\M\\S R\\MaMaS ReMeS ReMjMjS RhXk[ RfWjY R^WaY RcCiJ R_JhIlJ R]XZ[ RfWb[ RgTeY R^T\\Z RcC\\JZK',
	    'PoTGjG RUM^M RROlO RUR^R RUU^U RTDUG RUJUM RUOUY R^O^[ RUJ^J^M R_C_G RjDjG RhJjM RjRdY`Z RkXk[i[ RdJfX R`XZXTY R^XWY',
	    'Po\\ObO ReQlQ R\\TbT RRUYU RcUlU R_[b[ RRHRX RUDUU RYHYW R`I`T R[KbJb[ RhMh[ RgEeK RdNdT RjKgK R]FaF_M R^C[J\\TZZ RdEkEjL',
	    'Po[FlF R\\LjL R\\OjO RRWYW R]YhY Rf[j[ RRHRW RVDVW RYHYX R\\H\\O R]S]Y R`C`G RgCgG R\\HjHjO R`O[T RcS_W RbUgW R^RkRj[',
	    'PoTGkG RVKkK RWMiM RWPiP RWSlS R\\YdY Rg[l[ RTDTG RTUT[ RXIXS RTU\\U\\Y R`C`G RaIaS RdUdY RkDkG RdUlUl[ RYHRN',
	    'PoTGjG RSJkJ RSV]V R`WkW R_[l[ RTDTG RYTYZ R_C_G RaNaS ReTe[ RjDjG R]K`NaQ RcMfQkS RaJTM R^MTP RhLbM]PTS R`T\\T R\\ZT[',
	    'PoYHcH RXKfK RRQlQ RVZhZ RVVV[ RVEVO R_T_Z R_G_O RVEhEhO RiVi[ R]KfN R_J\\MXN R^QYSRU R`QdSlT',
	    'Po]JkJ RfNkN R]RaR RfRkR R\\VaV RfVlV RRWYW RRHRW RVCVW RYHYX R]E]J R\\NaMa[ RaEaJ RfLf[ RfEfJ R]EkEkJ',
	    'PoTGiG RZNaN RYQaQ RTDTG RWPW[ RZJZN R^I^N R_C_G RZT`T`Y RaJaN RiDiG RgVl[ RhUb[ RWISN RWNRT ReQiY RjPfX RcW_Y ReMgW RdMkMgW RZTYZ ReIcR',
	    'PoTGjG R`JkJ RTN]N RTR]R RfUkU RRU^U Re[l[ RTITN RTDTG RfPfZ RjDjG RaVdZ RbLhO RXW]Y R_PlPjS RUOSS RjKeM RXWUZR[ R_C_G RaS_Z R\\JTK RXQXX',
	    'Po[SlS RRUYU RRIRX RVCVU RYIYW R\\N\\Q R\\E\\H RcPc[ R\\NkNkQ RcRjY R_HbK RgHlL RlEjG RbEaH ReEdH R]K[L RfKdL RcS^XZZ R_C^J RhCgJ',
	    'Po[EkE R\\HjH R]MiM R\\OjO RZRlR RbUlU RRUYU R[YaY Re[i[ RRIRX RUCUU RYIYW R[T[Z R[TaTaY RcCcH RcMcO RiSi[ R[JlJkM RdWeY',
	    'PoUGjG R_JlJ RVN\\N RaPjP RaTjT RaWjW RZX]X RUDUG RXRX[ RSR]R]W R_C_G RaMaW ReJeM RjDjG RaMjMjW RZI^N RXIRO RhYl[ RcY^[',
	    'PoTHjH RTK`K RRNlN RUR`R RUT`T RUW`W RUZ`Z RTDTH RVQV[ RZIZN R\\P\\Y R_C_H RjDjH ReWi[ RhTbZ RVQRU ReKiL RiQgV RlWk[i[ RbJdV',
	    'PobGfG R\\O`O RRTYT RZUlU RRHRW RUCUT RYHYV R\\E\\U RbNbU RbCbKeKeU RgEjEjU RhWk[ R_D\\F R^XX[ R\\J_J\\J RgJjJgJ RgPjPgO',
	    'PoTGjG R\\JbJ RZMcM RcOlO R[PbP R[RbR R[ZbZ RTDTH RXOXY R[U[[ R_C_G RjDjH RiJlM RYJSO RXQSV R[UbUcZb[ RgIgQjZ RhOeY RW[T[ RTJWMXT',
	    'Po\\IkI R[LlL R\\RjR R\\TjT RRWYW RRHRW RVDVW RYHYX RcIcL R_[gZh[ R\\OjOjT RaVeW RiWlY R]F_H RjFfI R[EcEcH R\\WZ[ R_W_[ RjCcD',
	    'Po\\EkE R]JaJ RfJjJ R]MaM RfMjM R[PlP RRWYW RRHRW RUDUW RYHYW R[H[K R\\T\\[ RbSb[ RdEdN RfTf[ R[HlHlK R\\TkTk[i[ RbPaW',
	    'PoTHjH RRQ`Q RUT_T RUW_W RUZ`Z RTDTG RUQU[ R[P[Z R_C_G RjDjG R]J`M RgJiL RUKSN RYI[NTQ RjScZ`[ RlXk[i[ RdJfW RhN`O RkNcO',
	    'Po\\DlD R^HlH R_NjN R_QjQ RRWXW R`XiX RRHRW RUDUW RYHYX R^T^[ R_K_Q RbFbJ RdKdX RgFgJ R_KjKjQ R^TkTk[h[ R\\D\\QZY',
	    'PoWKkK RWLjL RWNjN RWQlQ R[ZeZ Rg[k[ RTST[ RWJWR R`C`G RaHaQ R[WeVeY RTSlSk[ RTDTGlFkG RXHTM RcTiW R]UWV RZV[Z',
	    'PoTGjG RaJkJ RSJ^J RRO_O RaPjP RaRjR RTT]T RaUjU RTW]W RaXjX Ra[l[ RZ[][ RTDTG RTQT[ RXHXK RTQ]Q][ R_C_G RbPbZ RaHaMjMjP RjDjG RUKVN R[K[N',
	    'Po[FcF ReFlF R[LcL ReNkN ReQkQ R\\RbR ReTkT R\\VbV ReWkW RRWXW RRHRW RUCUW RXHXX R\\O\\[ ReCeIkIkN RfNe[lZl[ R]G]J R_C_F RaG`L R\\ObOb[_[',
	    'PoTGkG R`IlI RRJ^J RTO]O RTR]R RTT]T RRW_W RTDTG RTMTW RTM]M]W R_C_G RaMaW ReIeM RaMkMkW RkDkG RhXkZ R[X]Z RVXS[ RXHXM ReWcZ_[ RfPeX',
	    'Po^SiS RRWYW R\\ZjZ RRHRW RUCUW RYHYX R[L[P R\\U\\[ R^O^S R^OiOiS R\\UjUk[ R[LlLlP R^HbI RhGkJ RlEjG RcEaG R\\E\\G ReEeG R^I[J RfJdJ R_C^I RhCgI',
	    'Po_EdE R_NdN R]PeP R]UdU RRUXU RRHRX RUDUU RXHXW R]R]U R`I`M R_C_H R]RdRdU R[HdHdK RiEjH R^W_Y RjOeZ RlWl[ RbWcY\\Z RgCgOiZ R[H[SZZ RbK\\K RlKeL',
	    'PoTGkG RRL_L RUN\\N R`SiS RSU^U RRX_X Rg[k[ RTDTG RUJUN RYNY[ R\\J\\N RSQ^Q^U R`C`G ReNeZ RkDkG RdSiX RhNhQ RdU`Y RbObQ ReRbX RbI_O RSQTU RaMlLlVk[',
	    'Po\\JjJ R]LbL ReOlO RRUYU R\\WjW RRIRX RUCUU RYIYW ReJeO R\\HjGjJ R[TjSjW RdWd[lZl[ R_EfEcG RgXiY R^QfQcSbW`YY[ R[OaO[S\\V RjLdM R`C\\G]O',
	    'Po\\GbG ReGkG R\\JbJ ReJkJ RZTlT RRWYW RRHRW RUDUW RYHYX R\\DbDbM ReDkDkM R\\D\\MaMbO ReDeMjMlP RfNdP R]N[Q RaR_V R^Wk[ RhUdY[[',
	    'PoTGjG R]MlM RSN[N R_OiO RSS\\S RWV\\V RhZjZ RSKSS RSUSZ RTDTG RVIVK RWSWZ RSK[K[N RSP\\P[S R_C_G R_U_Z RdSd[ R_JiJiO RjDjG R_UjUjZ R^R^SkRkT R\\YRZ',
	    'PoTKkK RWM`M RXS_S RXV_V RUY_Y RXPXY R[M[P R_C_G RUDUGjGjE RWHXI R^H_I RhGeJ RcPeViYl[ RdLaT RTKTUS[ RcPlP RjPgVdYa[ RVP`P R^P^[',
	    'PoTGjG RRL^L R_OkO R_SkS Rf[l[ RTDTG RSJYJYQ R_C_G R_K_S ReKfSf[ RjDjG R_KkKkS RhThXkWkX RXN^P RTX\\Z RWRTX RXLVORQ R`Z][ RbSaY RRT]TZYR[ ReOfW R\\IUJ',
	    'PoZEkE R`JeJ R]J_I R`LeL RcQiQ RRUXU RRIRX RUCUU RXIXW R[I[O R`GeGeN R`NdNdQ R]G^NYO RcEaGaN_Q[R RaR]VYX R`WdYk[ RhGkGkKgK R_UhUeXaYZ[ RhGhOkNkO',
	    'Po\\FlF R]McM ReMkM R_QkQ R_TkT R_WkW RRWZW R_ZlZ RRHRW RVCVW RZHZX R`P`[ RaCaG R]IcIcM RfOfZ RgCgG R`O\\S ReIkIkM R^I]M ReIeM',
	    'PoSGjG RRK^K R`MjM R`PjP RTP\\P R_VlV RVYZY RZ[][ RSSS[ RSDSG RTMTP RVUVZ RXIXK RVUZUZY RTM\\M\\P RSS]S][ R_C_G R`J`P RhRh[ RjDjG R`JjJjP RfR_S RcSbZ',
	    'Po\\FbF ReFkF RdHlH RiMlM R]QjQ R]TjT RRUYU R]WjW RRIRX RUCUU RYIYW R]N]W R]NjNjW R]D\\G ReDdG RhChGiK RgYl[ R_C^H\\M RfHeM RaY[[ R[HaIaL_L',
	    'Po\\DbD R[FcF R\\IbI R\\KbK RRLZL RdLlL R\\QbQ RUZiZ RUTU[ RVKVR R\\M\\R R_S_Z R\\MbMbQ RhKhR RiTi[ RSFUI RdFgI RjFeK RUCTG RXGSK RgCfG RYMYQ RjMlQ RSMRR ReMdQ',
	    'PoSGjG R`IkI RTL]L RaPjP RVP]P RVR]R RaSjS RVU]U RaWjW RSW_W RSNSW RSDSG RTHTL RVNVU RVN]N]U R^C_G RaLaW RfIdL RjDjG RaLjLjW RhYlZ R[Y]Z RWYR[ RdY_[ R[ITJ',
	    'PoTGjG R`IlI RRJ^J RTO]O RaPjP RTR]R RaSjS RTT]T RaWjW RRW^W RTDTG RTMTW RXHXM RTM]M]W R_C_F RaLaW RfIeL RjDjG RaLkLkW RhYkZ RZX]Z RVXS[ RdY_[',
	    'PoZFlF RcIlI R[LaL RbMlM RcOkO R[QaQ R[TlT RRWXW RRHRW RUCUW RXHXX R[I[Q R[IaIaL R[NaNaQ RgMgR R_DaF RjJgM RhDfF RdJeM RaS^Wj[ RhUdY[[',
	    'PoTMkM RWO`O RXT_T RXW_W RUY_Y RXRXY R[O[R R_C_F RcReVhYl[ RdNaU RTMTTSZ RUDUFjFjD RUI^I^KUKUI RaIjIjKaKaI RcRkR RjRhVeYa[ RVR`R R^R^[',
	    'Po]HbH ReHkH R\\JlJ R^MdM R^RcR R^UcU RRUYU RRHRX RUDUU RYHYW R^O^X R`MaO R]DbDbH R\\OdOc[ ReDkDkH RhWl[ RfQjY RjRfZ RfPkPhX R]D]H ReDeH RgLeT RcW\\X R\\J\\TZ[',
	    'Po_EdE RdKlK R[J`KcL R[NcN R]RbR RRUXU RaVdV RRHRX RUDUU RXHXW R[G[U\\[ R_H_L R_C_G R]XaX_[ R`TaV R]P]QbPbR R\\TcTc[ R[GdGdI RjEkH R^T^W[W RhChNjY RZQYZ RhKfZ',
	    'PoZFlF R`JeJ R`MeM RdRjR R_UhU RRUXU RRIRX RUCUU RXIXW R[J[P R`HeHeO R_CaF RbFbH RgDeF R`H`OdOcR R]H^OZO RaQ[S R`UYX R_WdYk[ R_J]J RhHkHkLgL RfWbYZ[ RhHhOkOlO',
	    'PoTGjG RZIaI RaNlN RVM[N_N RVP`P R\\W_W RTDUG RTKUVV[ RXKYN RZHZK RXY[YZ[ R\\U\\W R_C_G RjDjG RhJjL RYUXW RgSk[ RbZ`[ RWS[S RTKaKaM RfHfOdW RWQWR^Q^S[S RfMhX RTQS[ RVU_U_[][',
	    'Po[P]S RROTSX[ R_Tb[ ReLdPfSiUk[ RWESN R]K[P R`F]L RhHfL RjEgK',
	    'PoRPVT RVIULRO RWUZ[ RjEhHfJbOjXk[ RYEVJ',
	    'PoaFaW RlEl[ RVEVQUX',
	    'PobDbY RlDl[ R\\K^Q RfJhP RUJRP RYDYPXUUZ',
	    'PoRGkG RVMiM Rf[l[ RVGVM R^P^Z RgPg[ RlXl[ R^C_F RWWR[ RWQVX',
	    'PoSEkE RUSjS RR[l[ R_S_[ R\\LaQ RTLYQ RcLhQ RhHcL RXHTL R`H\\L',
	    'PoSDWH RRNVNVWR[ R^DZM^W RdD`MdW RjDgMkW RUYa[l[',
	    'PoVNiN RVRiR RRUlU RVJVR R_J_[ RVJiJiR R_UjZ R_US[ RXCUFXH R`C]F`H RgCdFgH',
	    'PoVLiL RVPiP RSTlT RVGVP R_G_[ RVGiGiP RUDXF R^C_F RhCeG R_TbVlZ R_T\\VRZ',
	    'PoUOiO RUJUO RVQVZ R_Q_Z RUJiJiO RbReS RUFXH RaUeW RYR\\S RXU[W R\\F`G ReFhH RXCUF R_C]F RgCeF RZKeN RdKYN RhRiZ R[YR[ RfY\\[ RlYl[j[',
	    'PoSGkG RR[l[ R_G_[',
	    'PoSIlI RZPjP RW[l[ RbPb[ R\\DZOVVRZ',
	    'Po]ElE RRG\\G Rc[h[ RWGWT RbE_P R]SRU R`NkNi[',
	    'PoREkE RRSiS RRZlZ RRER[ RRKiKiT',
	    'PoRG\\G R\\NfN RWGWT R_VZ[ RlVl[ RaGaR]Y R\\SRU R\\EiEiUjZ RaL_V',
	    'PoTDlD RTLiL RTPlP RTDTP RRTjTj[ RTHiHiL',
	    'PoSFkF RR[l[ R_F_[ RWO\\T ReOkU RXJWPSV RgJePaV',
	    'PoSE\\E RgNkN RUQjQ RRUlU RXEXL R_DgDgM RlJkM R_O_UaWdYl[ R\\KRM R^U\\XXYR[ R_E_L',
	    'PoSE\\E ReMjM RTQjQ RRUlU RXEXL RbJ^N R_O_TbWfYl[ R_U\\XXYR[ R^FkFjM RcCbK R\\KRM',
	    'PoTGkG RUKiK RRPlP RZSiS RWZkZ R_G_P RaSaZ RYDZG ReCdG R[PWVR[',
	    'PoSE\\E R^GlG R_MkM RSQ\\Q Rh[l[ RXQXX RdPdZ RiPi[ RlXl[ RYJ\\M RgIlN ReGbJ^M RdCfG R[FWLRM R]Z[[ R\\WRY R`Q_X',
	    'PoRE^E R_FkF R`MjM RRR]R Ri[l[ RXRXY RePeZ RiOiZ RlWl[ R[K\\O RiJkM R\\G[L RdFcJ`L RWKYP RRKTP ReCfF RTGRK RXGVK RaQ`Y R^XRZ',
	    'PoRNhN RS[j[ RSNSZ RREhEhP RlVk[',
	    'PoTOhO RU[j[ RUJUZ RREhEhP RlUkZ',
	    'PoRNiN RS[j[ RSESZ RREiEiP RlVj[',
	    'PoROiO RS[j[ RSESZ R^E^O RREiEiQ RlVj[',
	    'PoUE^E RUJlJ RYTjT RZ[k[ RZNZZ RbNbT RYNjNjT RlXj[ RUEUQTY RbD]E RiCaD',
	    'PoRE^E RYN]N RaOjO RSY]Y Ra[k[ RSQS[ RSQ]Q][ RaEaZ RaEjEjP RlWl[ RVKRO RXEUL R^F]N',
	    'PoTGjG RRMlM RYVeV RY[h[ RYPYZ RXQeQeV RdCdMjT RiXh[ RZCZNTT',
	    'PoRE^E R`OkO RXS]S RRY^Y Ra[k[ RRER[ RUJUT RXSXY RXEXK]K]S RaEaZ R`EkEkP RlWl[',
	    'PoVEgE R[N_N RSPkP RUVhV RV[j[ RVVV[ R_G_M RUShShV RjXj[ RbGeKlN RSH[HXKRN RjFdI RfE_G',
	    'PoTJ]J RaJkJ RTO^O RbPkP RRSlS RTETO RTE]E]J R]M^O RbEbO RaEkEkJ RkMkP RiXk[ RcVgW RZUWYR[',
	    'PoTHjH RRMlM RXUdU RX[g[ RYUY[ RYQdQdU RWDYF RhXg[ RaHcLhQlR RfDdH R^E[MVRRS',
	    'PoTJiJ RTOjO RRY\\Y RaZkZ RRRR[ RTETN RRR\\R\\Z R^E^I RaRaZ RTEiEiI RkLjN RlWkZ RkS`U',
	    'PoTH\\H RaHjH RTQjQ RRVlV RZNZV RSD\\D\\H RTHTM]M]K RdNdV RaDjDjH RbHaMjMjK RcXl[ R[XR[',
	    'PoeWlW RRIRW R_C_[ RRIlIlW',
	    'PoRHlH RdWiW RUMUW R_C_[ RUMiMiW',
	    'PoRElE RdXiX RUKUX R_E_[ RUKiKiW',
	    'PodWjW RULUX RRF`F`[ RULjLjW RdE_E RkDdE',
	    'PoRGlG RcWiW RUMUW R_C_[ RUMiMiW',
	    'PoSHlH ReXjX RXMXX RaJa[ RWOjOjX R]DZKRR',
	    'Po\\FlF Ra[f[ RRHRU RWCW[ RfFf[ RRH\\H\\UYU',
	    'PohWlW RRGRR R]H]W ReCe[ R]HlHlV RXCXSTZ RXLVW',
	    'PoXU[U Rh[l[ RRIRU RVCV[ RRI[I[U R_EhEiZ RaLfQ RlWlZ R_E_Q]X',
	    'PoRJlJ RUNhN ReYiY RURUZ R_C_[ RUFhFhN RURiRiY',
	    'Po[ElE RgWjW RRFRS R\\J\\W RcEc[ R\\JjJjW RWCWQVX',
	    'PoXUZU Rd[h[ RRIRU RVCV[ RRIZIZU RaW][ RaEgDkM R`F\\N RbOaX R^OjOiUh[',
	    'Po_OjO RYU[U R_[k[ RRIRU RWCW[ RRI[I[U R_E_Z ReEeO R_EjEjP RlWkZ',
	    'PoUIlI RdYiY RURUY RUEUN R_O_[ RURiRiY RlMkP RUE_EaJeNiO RgC_D RYNRO R[NTN R^MWN RaMZN',
	    'PoRMlM ReYjY RYQYY RaNa[ RWRjRjY RVDiJ RhCbGVJ R]I\\MUT',
	    'PoVF]F RVNlN RhYlY RYRYY RbOb[ RVIlIlN RYRlRlY RVEVQTY R`E[E RcD]E RjC`E',
	    'PoXUZU Re[k[ RRIRU RVCV[ RRIZIZU R]I]M ReNe[ R]IkIkM R`U[[ RcCcO_X RlWl[ RcIbT',
	    'Po]GlG R^LkL RXU[U RhXkX RRIRU RVCV[ RRI[I[U RdCd[ R]QlQkX',
	    'Po\\NlN RWUZU RRIRU RVCV[ RRIZIZU R_D_[ RfUlZ RjEcK RdNhW ReY^Z',
	    'PodYhY RVRVY R_P_[ RVRhRhY RbFcJgMlO RTJ]N RWCSK RRF]F[KYMRP R`EjEiIeM`O',
	    'PocHkH RaHiH R^HfH R\\HcH RZI`I ReYiY RVRVY RXFXO R_O_[ RVRjRjY RYDRK R`EcKeNiO ReDjE R`CbK RlMlOiO',
	    'Po\\HlH R\\OkO RXUZU RRIRU RVCV[ RRIZIZU RdCd[ RfSlY RaS[Y RdMgU RcMaT',
	    'PoXUZU RRIRU RVCV[ RRIZIZU RdCdN R_NbTfXl[ R^NjNhTcY][ R^HkHkL R^H^R\\Y',
	    'Po`QlQ RYU\\U R`ZlZ RRIRU RWCW[ RRI\\I\\U R`H`[ R`HlHl[ RfCdH',
	    'PodIlI RYU[U R_YjY RRIRU RVCV[ RRI[I[U R_O_[ RdCdO R_OjOj[',
	    'Po]JlJ RXUZU RRIRU RVCV[ RRIZIZU ReUk[ RfUa[ RaCaNfW RfDjF RiOdX R`R\\Z RaJdS RiLfV RaI_V',
	    'PobMjM ReYjY RRFRI RTQTY R_C_F R_M_[ RcFcM RTQjQjY RRFlFlI R[FZJXLSN',
	    'Po_IkI R]OlO RYU[U RRIRU RVCV[ RRI[I[U RgVk[ R_Y[[ RbT]Z RdCdOgX R_E]M RdKcT',
	    'PoRHlH RVKgK RdZhZ RSOSR RVSVY R_O_[ RVDgDgK RVShShY RSOkOkR',
	    'PoTJjJ RTNjN RfZlZ RRRRZ RTFTN R_N_[ RTFjFjN RRRlRlY R_C]F',
	    'Po_PkP RYU[U RRIRU RVCV[ RRI[I[U R_E_P R_EkEkP RiTl[ RaT^[',
	    'PoRFlF RdYhY RVQVY R_C_G R_N_[ RVQiQiX RRLlLlP RXHZK ReHdK RRLRP',
	    'Po]ElE R^RkR RYU[U R^ZkZ RRIRU RVCV[ RRI[I[U R^K^[ R^KkKk[ RdEcK',
	    'PoRFlF ReYiY RUQUY R_C_F R_O_[ RcFcO RUQiQiY RfIiJlM RWISM R\\KWO R\\F[M',
	    'PoSI]I RaIkI RSNkN RfZlZ RRRRZ RSESN R_N_[ R`EkEkN RRRlRlY R^DSE',
	    'Po^IlI R]QlQ RXU[U RRIRU RVCV[ RRI[I[U RhIh[ R`DaH RhCfI RaIaQ`W',
	    'Po_LhL RXUZU R^ZjZ RRIRU RVCV[ RRIZIZU R^Q^[ R^QjQj[ RdElM RbF\\M RcCaI',
	    'PoTIjI RRMlM RcZgZ RWRWZ R_O_[ RWRgRgY RWDYG RfDcG RcMfQlS R^E[MVRRS',
	    'PocOlO RfZkZ RVSVZ R`P`[ RcFcO RVSkSkY RcFlFlO RRG_G]KYNRQ RXCTK_O',
	    'PoRMZM RhVlV RRY[Y RRGRZ RRGZGZM RRR[R[X R^H^V ReCe[ R^HlHlV RVCTG',
	    'PoRGlG RdYhY RSMSQ RWQWY RWCWJ R_C_J R_M_[ RgCgJ RWQhQhX RSMkMkQ',
	    'PodFlF RRIRU RVCV[ R_J_V RdCdJ R_JjJjV RfXl[ ReMdUaY\\[ RRI[I[UYU RdPdW',
	    'Po^RiR RXUZU Rf[l[ RRIRU RVCV[ RRIZIZU R^K^R RgRg[ R^KjKjR RlXk[ RaW][ R`G\\J RaDhDjI RbR`Y',
	    'Po_PlP R_UlU RXU[U Rh[l[ RRIRU RVCV[ RRI[I[U R_K_[ ReCeK R_KlKl[ RkEjH R_EaI',
	    'Po]EkE R`JkJ R]NlN RXUZU RRIRU RVCV[ RRI[I[U RaNa[ RgVlZ RkQhS RePiX RfY`Z R]E]Q[Y',
	    'Po\\ElE RRNZN RhWkW RRYZY RRGRZ RRGZGZM RRRZRZX R^J^W RdEd[ R^JkJkW RVCTG',
	    'PoRGlG RUJhJ RXQiQ RdZiZ RVTVZ RXLXQ R_Q_[ RVDhDhJ RWMiMiQ RVTjTjY R]D\\HXLRO',
	    'PoUFlF RWKlK R\\PfP ReZjZ RYSYZ R\\H\\O R`C`F RaPa[ RgHgO RYSjSjZ RUFUSSZ',
	    'PoSF`F RSI`I RRM`M ReOkO ReZjZ RUSUZ R_Q_[ RcDcQ RUSjSjY RiJlM RcElEhI RZCYMUQ',
	    'PoRFlF RWKgK RdYhY RWRWY RWCWK R_C_K R_N_[ RgCgK RWRhRhY RSNkNkR RSNSR',
	    'Po\\GjG R[KjK RfYjY RRFRR RZNZQ R\\R\\Y RcNc[ R\\DjDjK R\\RjRjY RZNlNlQ RWCWQVX',
	    'Po\\GkG R]JkJ R[NlN R_SlS RXUYU Rd[h[ RRIRU RVCV[ RRIYIZU RhPh[ RaUdX R^TZY RaM]V RcD`R',
	    'Po\\IcI ReIlI RcQlQ RXUZU RRIRU RVCV[ RRIZIZU RhHh[ R_W[[ RjCgI RcP\\R RbC`I`R_X',
	    'Po_EjE R_HiH R_LiL R\\PlP RXUZU RRIRU RVCV[ RRIZIZU R_E_Z RjSeU ReY[[ RcPeVj[',
	    'PodGlG RaGiG R^GeG RaRjR R]SeS RXUZU RRIRU RVCV[ RRIZI[U RkKlN RlXk[ RaQdX RaEcJeLjN RdOhP RiTdWfZj[ RdDhE RgV][ RaOcW RhIbL\\N',
	    'PohUkU RXUZU Re[l[ RRIRU RVCV[ RRIZIZU R\\G\\J RdCdG RfLf[ ReLkLkT R\\GkGkJ RlXl[ R^QaT R`V[[ R_J[R RbP^X R^McM`V',
	    'PoRGlG R\\KbK RdZhZ RVRVZ R\\D\\K R_N_[ RbDbK RVRhRiY RhDgLlLlJ RSNSR RXDXITL RSNkNkR',
	    'Po^JkJ R_NkN R_TkT RXUZU R_ZlZ RRIRU RVCV[ RRIZIZU R_H_[ RfHfZ RaC]L RhDeJ',
	    'PoWPgP RdZjZ RRHRL RTTTZ RWKWP R_C_H R_P_[ RWKgKgP RTTjTjZ RRHlHlL RUDXF RiDfF',
	    'Po\\FkF R\\MlM R\\RlR RRIRU RVCV[ R^HaK RbDeF RaR^U RbN`S RhHfM R]VkZ RRIZIZUXU RhRgVdY[[',
	    'Po\\GlG R_LhL R_PhP R\\TlT RXUZU RRIRU RVCV[ RRIZIZU R_C_T RhChT RgVl[ R`V[[',
	    'Po\\FlF R]IkI R[LlL RRIRU RVCV[ RRIZIZU R^O^V RdCdL R^OjOjV ReXl[ RbYZ[ RdRcW',
	    'Po`IiI RaOiO R_UjU R]YlY RRIRU RVCV[ R]D][ RdIdU R]DlDl[ RgQiS RRIZIZUXU',
	    'Po^HlH R^LlL R_SkS RXU[U R_WkW R_ZkZ RRIRU RVCV[ RRI[I[U R^D^L R_O_[ R_OkOk[ R^DlDlL',
	    'Po]GgG R\\LlL RXUZU R`UjU R`ZjZ RRIRU RVCV[ RRIZIZU R`P`[ RbCbL R^QjQj[ RfNiO RjFaO[R',
	    'Po`JlJ RaOkO R_TlT RRIRU RVCV[ RRIYIYU R^G^[ RbEiEiJ RgWk[ ReVaZ RfNfThY R_CYO RbK_R',
	    'PodFlF R^NjN R^RjR RXUZU R^VjV RRIRU RVCV[ RRIZI[U R^J^V RdCdJ R^JjJjV RhYkZ RbX\\[',
	    'Po\\ElE R^MkM R^RkR RYU[U R^VkV RRIRU RVCV[ RRI[I[U R^I^V RdEbI R^IkIkV RgXl[ R`XZ[',
	    'PoWJgJ RWMgM RSPlP RbZfZ RTETH RWGWM RWSWZ R_R_[ RVTgTgY RWGgGgM RTEkEkH RdRkU R^MYRRU',
	    'Po\\JlJ R^QjQ R]TkT RXUZU R]XlX RRIRU RVCV[ RRIZIZU R]U]Y R^L^Q ReQe[ R^LjLjQ R^FiFiJ RbCaJ',
	    'Po]IkI R]LlL R^QkQ RXUZU R_UkU R]ZlZ RRHRU RVCV[ RRHZHZU RdRdZ R]DkDkI RhNkR RcLaQ R]D]R\\Y',
	    'Po]ElE R^MjM RXUZU R]VkV R]ZkZ RRIRU RVCV[ RRIZIZU R]Q][ R^H^M RdQdZ R^HjHjM R]QkQk[',
	    'Po^HjH R^LjL RXUZU R^XiX Rf[j[ RRIRU RVCV[ RRIZIZU R^D^L R^S^X R^DjDjL RaN\\R RdRaV RdThV R_PlPlUk[',
	    'PoSE]E R`GlG RRI^I RSL^L ReQiQ RdZiZ RVSVZ RXCXI R_Q_[ RiDiQ RVSiSiZ RbJdM RXJXPRQ R[PUP R_OYP',
	    'Po]IkI R^OlO R^TlT RWUZU RRIRU RVCV[ RRIZIZU RhMh[ R]DkDkI RaW^[ R]D\\R[Y RbLbObTaX',
	    'Po\\HlH RcLhL R^OkO R\\SkS RXUZU RRIRU RVCV[ RRIZIZU R^D^O RcDcL RdOd[ RiDiL RdSjY RdS^XZZ',
	    'Po^DjD R\\HlH R^OkO R^SkS RXUZU R^VkV RRIRU RVCV[ RRIZIZU R^L^V RdDdH R^LkLkV RgXkZ RaXZ[',
	    'PoRK]K RZQ\\Q ReZiZ RUSUZ RYLYO RUF]F]Q R_Q_[ RUSiSiY RhIlHlI RXGYI RcMjQ RXCXF RbEaI R`KjKgN`Q RbDhEhI RUFUKTP',
	    'Po]HjH R]LjL R\\SlS RXUZU Rf[l[ RRIRU RVCV[ RRIZIZU R]D]L RdLdS RfSf[ R]DjDjL R^N_Q RiNhQ RbS`X^Z[[ RlYl[',
	    'Po^TdT RfTlT R^ZlZ RRIRU RVCV[ R_O_[ RfNlNl[ R^H`L RdHeK RkGiL RdN^O RRI[I[UYU RkD`F',
	    'Po^MjM R^QjQ R\\TlT RXUZU RRIRU RVCV[ RRIZIZU R\\E\\H R^I^P R^IjIjP R\\EkEkH RgVl[ R`V\\[ RdQdT',
	    'Po^KiK RRIRU RVCV[ R\\G\\J ReQeY R\\GkGkJ RiUlX RgPjV RcDdG RcK`N\\O RdO`R\\T ReR`W\\X RRIZIZUXU Rd[`[ RjNdOeV',
	    'Po^IjI R\\MlM RgYiY R^R^Y RdOd[ R^RiRiY RVIXK RVOYR RgMlQ RhDlH RaM\\Q R_E\\H RfIhO RcG_O RcDaM RTEZEZ[W[ RTETSS[',
	    'PoZJbJ RYMbM RWKW[ RZQZX RZEZJ R^C^J R^M^[ RbEbJ RgVb[ RWDRH RWIVMRP RfRjZ RiPeY ReMhX RZQbQbX`X ReKgV ReJkIhU RfCdO',
	    'Po^QkQ RXUZU Ra[h[ RRIRU RVCV[ RRIZIZU R^F^Q RaTaZ RhWhZ R^FkFkQ RcSfV RbKgM RcDaF RcG`K RjTkY R]U\\Y RbIhIdN`O',
	    'Po]GjG RWUYU RRIRU RVCV[ RRIYIYU ReLjKkM RgDjH RgHeL RaC^H[L RdMlP RdM\\P RdP]S RgS^W RjUcY][ RaJbL[L',
	    'Po^GjG R^JjJ R\\QkQ RWUYU RRIRU RVCV[ RRIYIYU R\\M\\Q R^D^J R^DjDjJ R\\MkMkQ RaMaQ RfMfQ R^SbWfYl[ R\\TjTgWcYZ[',
	    'PoSEkE RWLhL RWOhO RRRlR RdZgZ RWHWO RWTWZ RZCZG R_R_[ RdCdG RVUhUhZ RWHhHhO RdRgTlV R\\OWURV',
	    'Po]ElE R]MkM R^PjP R\\TlT RXUZU Ra[d[ RRIRU RVCV[ RRIZIZU R]H]M RbEbM RdTd[ RgEgM R]HlHlM RhVlZ R`V\\Z',
	    'Po_JjJ R_QcQ R]ZlZ RRIRU RVCV[ R]E][ R_McMcQ R]ElEl[ RhGiH RgRbX RiMfU R_M_Q RRIZIYUXU RfFfOhW RjUiXjX RdT^T',
	    'Po\\EkE R]HjH R\\JlJ R^PjP R^TjT RXUZU R^WjW RRIRU RVCV[ RRIZIZU R^M^W RdCdJ R^MjMjW RgXl[ RaX[[',
	    'Po\\FcF ReFlF R]NjN R]QjQ R\\UlU RXUZU RRIRU RVCV[ RRIZIZU R]J]Q R`C`H RhChH R]JjJjQ ReWhXkZ RbV`Y[[ RcQaX',
	    'PoSE`E RSH`H RRK`K RgLkL RWQiQ RWTiT RfZjZ RUVUZ RWOWT RZCZJ R_T_[ RcDcM RWOiOiT RUVjVjZ RcElEhHlI RYKSN',
	    'Po\\FkF R\\JlJ R]PjP R]SjS R[WlW RRIRU RVCV[ R]L]S RdCdF RdSd[ R]LjLjS R`F`J RhFfJ RRIZIZUXU',
	    'Po]HjH R]KjK R[NlN R]UjU RWUZU R`[c[ RRIRU RVCV[ RRIZIZU R]D]K R]Q]U RcUc[ RcLcN R]QjQjU R]DjDjK RhWlZ R^W[Z',
	    'Po]IcI ReIkI R^PjP R^SjS RXUZU R\\WlW RRIRU RVCV[ RRIZIZU R]E]I R^L^S R]EcEcI RdLd[ ReEeI R^LjLjS ReEkEkI',
	    'Po\\IlI R\\NkN R]QjQ R[UlU RXUZU RRIRU RVCV[ RRIZIZU RaCaI RfCfI R]D^G RjDiG R_J`M RcNcUgYl[ RgJgM RcV`Y[[ RdQbX',
	    'Po[GeG RYMlM R\\TcT RWUYU R\\XcX RRHRU RVCV[ RRHYHYU R\\P\\Y R\\H]K R`C`G RbHbK R\\PcPcX RiEkI RkOhUd[ RgDgQhZk[lX',
	    'Po]HlH R\\NlN R[TlT RWUYU RRIRU RVCV[ RRIYIYU R^J^T RaWb[ RbJbT RfJfT RjJjT ReWf[ RiVl[ R_C[K R]W[[',
	    'Po\\JlJ RXUZU R]VjV R]ZjZ RRIRU RVCV[ RRIZIZU R]R][ RdRdZ R]RjRj[ R^F_I RdJhNlO RhFgJ RjDdE^E RdEdP RdJ`N\\P',
	    'Po\\FkF R[JlJ R]PjP R]TjT RXUZU R\\WkW R[[l[ RRIRU RVCV[ RRIZIZU R]M]T RdCcF RdMd[ R]MjMjT RgGgJ R`G`J',
	    'PobGlG R[P]P RcZiZ RRHRP RTSTZ RTJTN RXCXP RRH]H]P R^Q^[ RTSiSiY RRDTF R]D[F R[J[M RbGcJgNlP RcCaH RiFhJeM`O',
	    'PoVN[N RdZiZ RSHSP RUSUZ RVJVN RYCYH RVJ[J[N R_Q_[ RUSiSiY R\\D_G RUDRG RdCbGdJgNlP RbGkGhKeN`O RSH^H^O\\O',
	    'Po]SkS RXUZU R\\VlV Ra[d[ RRIRU RVCV[ RRIZIZU RdCdG R^PePeZ R_IiIiM R^D_F RjDgG R\\G\\K R\\GlGlK R_I_MiNcP',
	    'Po`FkF RTI]I R_JlJ R_NlN RUP]P RdZiZ RTDULUP RVSVZ RTD]D]H RUL]L]P R_Q_[ ReJeQ ReCfF RVSiSiY RbGbI RhGgJ RTGSP',
	    'Po_IhI R[PbP RePlP R[SlS RRIRU RVCV[ RRIYIYU R[K[P R_D_I R[KbKbP RcQc[ ReKeP R_DhDhI ReKlKlP RcRhXlY RcR^WZY',
	    'Po\\IlI RfLkL RaNjN R]QlQ R`SjS R`UjU RXUZU R`ZkZ RRIRU RVCV[ RRIZIZU R`W`[ RgIgL R`WkWk[ R_FgFdH RcIbK_M R`E\\I]R\\Y',
	    'Po]FkF R\\JlJ R]PjP R]TjT RXUZU R]WjW RRIRU RVCV[ RRIZIZU R]M]W R`H`L RdCdH RhHhL R]MjMjW RfXl[ RaX[[',
	    'Po[ElE R_LiL R]OkO RXUZU RRIRU RVCV[ RRIZIZU R\\I\\L R`C`H RgCgH R\\IkIkL RgUlZ RdOhW RdO\\S RcS\\V RjRfT RbRdV[Y RdWd[`[',
	    'PoSE^E R`FlF RRG_G RSJ^J RXM^M RdMhM RUQiQ RUTiT Rf[j[ RTVT[ RUOUT RXCXM R_T_[ RhChM RRMWNiOiT RbHdJ RTVjWj[ R[LVM',
	    'Po[EkE R\\HjH R]MiM R\\PjP RZRlR RcUlU RXUZU R[YaY Rf[i[ RRIRU RVCV[ RRIZI[U[Z R[TaTaY RcMcO RcCcH RiSi[ RZKlKkM RdWfY',
	    'Po[FlF R\\NkN R\\RkR RXUZU RRIRU RVCV[ RRIZIZU R\\I\\N R`C`H RaIaN RfIfN RgCgH R\\IkIkN RkYl[ ReWi[ RfPiP R]UbW RiTeY`Z RdOeX R\\R[Z',
	    'Po\\FlF R^JdJ RfMlM R^MdM R^TdT Rh[j[ RRIRU RUCU[ RRIYIYU R_V_Y RaHaM RcCcG RjHj[ RgQgU RcVbY R^P^T R^PdPdT ReY]Z R\\F\\RZZ',
	    'Po]GcG ReGlG R]OcO R]ScS RVUYU RhWkW RRIRU RVCV[ RRIYIYU R]L]Z R]LcLcS ReLe[ ReLkLkW R_F`I RhFiJ R^C[I RfCdI RaUcY[Y',
	    'Po]KcK R\\NcN R\\TkT RWUYU RRIRU RVCV[ RRIYIYU R[F[I R\\P\\S RcCdF R]IcHcN RaV`[hZh[ R\\PkPkT R[FlFlH RcUeW RhVlZ R]V[Z RaPaT RfPfT RhH`I RiJ`K RjLaM',
	    'Po\\HbH RfHlH R\\KbK RfKlK R^NjN R_UiU RRIRU RVCV[ RRIZIZU R\\D\\[ R\\DbDbK RdLdZ RfDfK R_QiQiU RdTiY RdT_Y R_Q_U RfDlDl[i[',
	    'PoTFkF RROlO R_F_[',
	    'PoTEjE RRQlQ R_E_[ RVHYN RgHdN',
	    'PoWGkG RWMjM RRTlT RWMWT RaGa[ RYCWHRL',
	    'PoSE]E R`EkE RRO]O R_OlO RXEX[ RfEf[',
	    'PoSIkI RRQlQ ReIe[ RYDZH RXVT[ ReDdG RYIYQXW',
	    'PoSI^I R`IkI R_RlR ReIe[ RgEdH R[CYH R^QRS RYIYRXWT[',
	    'PoUFiF RRKlK RSQkQ RTVjV R_C_K R_Q_[ ReLdP RYLYP',
	    'PoRG^G R`LjL RSN\\N R_RkR RSR\\R RRV^V RSJSR RXCXJ RXRX[ RSJ]J]R ReLe[ ReDcG_L ReDgGlL',
	    'PoRYkY RZO]R RdSlZ RdJVY R]C\\G R[HVL RTKXM',
	    'Poc[j[ RSJYP RXDUL R^FlFlRj[ R\\IUX^V R\\S`X',
	    'Poe[j[ R_JlJlTj[ RdCdPcU^[ RXDUMXP R\\JTX]V R\\T^X',
	    'PoRYlY RRFR[ R_D_Y RlFl[ RXEUMXO R[JUV[U R[S\\U ReEbMeO RhJbVhU RgSiU',
	    'PoaNkN RRN\\N RRRlR R[K\\O RSGWK RXU\\W ReOhP RbGeJ RhJkM RiFbM RZHTN ReDbG RWDUH RhSdW[[ RXSVWR[ R_D^J`QcXf[k[lW',
	    'PoUGlG R`C`G RUGURTY',
	    'PoUGlG R[[j[ R\\I\\Z R`C`G RkWjZ RhLcO[Q RUGUSSZ',
	    'PoUGlG RXMlM R\\[b[ RbMb[ R`C`G RUGUSSZ',
	    'PoTFlF RdTjZ R_DaF RaLeV R`O]VZYVZ RaJbQ RaI_T RTFTSS[',
	    'PoUGlG R`C`G R_J[Y RUGURSZ RXZjX RhTlZ',
	    'PoUGlG RXOkO RVZlZ R`C`G RaIaZ RUGUSSZ',
	    'PoUGlG RcYkY R^YeY RYZbZ RiWlZ RfSjW RfMYZ R`JXR R_CaG RUGURSZ',
	    'PoUGlG RXNkN RcSjZ R_DbG RaNdU RaN^VZYW[ RaI_T RUGUSSZ',
	    'PoTGlG RYO`O Rc[k[ RYJYZ R`C`G RdJd[ RkMcQ RlWl[ RTGTSS[ RaYV[',
	    'PoTFlF R`McP R_DaF RYShSa[ RaHdLlO RaH\\NWO RTFTSSZ',
	    'PoUFlF RXLjL RYUhU Ra[k[ RYOYU RaHaZ RhOhU R`CaF RlWlZ RUFTSSZ',
	    'PoUGlG RWNlN R`C`G RaIa[ RcPkX RUGUSS[ RaN_RXY',
	    'PoUGlG RWKkK R`C`G RaHaP R[Q`WeYl[ RXPhQcW^YW[ RUGURSZ',
	    'PoUFlF RYJkJ RZRlR Rb[f[ R[MZQ RfJf[ R`CaF RfQbU RaVXZ RUFURSZ',
	    'PoUGlG R[KdK R[QkQ RdQd[ R_DbF RUGTSSZ R[K[SYY RhJaK',
	    'PoUGlG R`C`F RePe[ R[WX[ RaHfMkO R`H[MVP RUGUSSZ R\\Q[X',
	    'PoUGlG RYJiJ R\\[a[ R`C`G RaQa[ RWQlQgU R]MeQ RgKaN RUGUSSZ',
	    'PoUGlG RZTiT R`C`G RZNiNiT RUGURSZ RZKZRYX RfI[K',
	    'PoUFlF RXKkK RWQlQ Rc[k[ RdQdZ RkXk[ R`CaF R_U\\YV[ RUFURSZ RaK_U',
	    'PoUGlG RXOkO RVZlZ RaIaZ ReSiV R_CaG RUGURSZ',
	    'PoUFlF RXKkK RZRjR RWVlV RcNc[ R_DbF R_H[Q RUFUSSZ',
	    'PoUGlG RWZlZ R_C`F RiMcY RYL[U R`KbT RiKfT RUGUSSZ',
	    'PoUFlF RZPlP RW[d[ R`C`F RUFUSSZ RaVYW RhHZKZW RdIdPgYk[lW',
	    'PoUFlF RZTcT ReVhV R[[k[ R[O[Z R`C`F RZOcOcT RkWk[ RUFUSSZ RZKjKiQhV R\\HZLWO',
	    'PoUGlG RaMlM RYZiZ RYRY[ R`C`G RaIaR RYRiRi[ RUGUSSZ',
	    'PoUGlG RYOjO RYTjT RYJYV RaJa[ RYJjJjV R_DbG RUGURSY',
	    'PoUGlG RYSjS RYZjZ RYMY[ RbHbZ RYMjMj[ R`C`G RUGURTY',
	    'PoUFlF RVNlN RXSiS R`C`F RXJiJiS RUFUSSZ RaHaR_V^XW[ RaTbVeYk[',
	    'PoTGlG R]OaO Rd[k[ RYMYZ R]I]Y RdId[ R_DbF RkWk[ RiNcQ RTGTSS[ RbXVZ',
	    'PoTGlG R^MlM Rc[h[ R`C`G RhHh[ RaRbV RTGTSS[ R\\I[MWQ R[M[[',
	    'PoUGlG R_DbF R_HYN R\\K^NcPlS R_TfU R]KgKdN^QWS R[XhZ RUGTSS[',
	    'PoUFlF RWMkM Rc[k[ RcMc[ R`CaF RcIgJ R[VV[ RlWl[ R]QZY RhQbW]Y RUFTRSZ R^I\\U',
	    'PoTGlG RXNkN R_CbF RfIiL R]SVZ R]SaWi[ RZIXN R_L[V R]RhScX\\[ R`I]S RTGTSR[',
	    'PoUGlG RXMkM RYQjQ RWVlV R`C`G RaMa[ R\\H]L RfHeL RUGUSR[',
	    'PoUFlF RWLlL R^Q^Z RfUkZ R`CaF RiQeR RcNgW RaM[SWU RdY\\Z R`IcKcQ RUFTSSZ',
	    'PoUFlF RWJkJ R\\PiP RXPfP RXUjU RV[l[ R`C`F RaRa[ RfMjQ R_J]O RUFURSZ',
	    'PoTGlG R`CaF R]VYZ RhJfN RYK[O RcRhU R^I^SWU RcIcZkYk[ RTGTSS[ R^O]W',
	    'PoUFlF RYKiK RWOlO RWSlS Ra[f[ R`C`G RaHaO RfPf[ R\\U_X RUFURSZ',
	    'PoTGlG R]MlM R[L[[ R`C`G RdId[ RgSlW RaS\\W RZMUR RdLhT R\\IYO RdLaS RTGTSS[',
	    'PoUFlF RWKkK R\\PfP R\\H\\P R`C`F RfHfP RUFUSSZ RXSiSfWbYX[ R[T^WbYl[',
	    'PoTGlG RXTjT RVZlZ R`C`G RaIaZ R[M^P RgMkQ R[IXQ RgIdP RTGTSS[',
	    'PoUGlG RZJhJ RWNlN R[QiQ RYVlV R][b[ R`C`G RaHaN RbTb[ RjIeNWT RhRbS RUGURSZ',
	    'PoUFlF R\\NfN RXSjS R][a[ RaNa[ ReUjZ R`C`F R\\UXZ RaHdKlN RaH]LWN RUFUSSZ',
	    'PoUGlG R`LlL RWM^M R`QjQ RW[[[ R[I[[ ReHeQ RbTfYl[ RjSeX^[ R`C`G R^RVT RbQaT RUGURSZ',
	    'PoTFlF RXJkJ RYQiQ RYTiT RVWlW RYMYT R`C`F RaGa[ RYMiMiT RTFTSS[',
	    'PoUFlF RWLlL R][k[ R]O]Z R`C`G RlXk[ RaHfJ RhN`Q RiQaU RkTeW`X RUFURSZ R[H[NZW',
	    'PoTFlF R_OlO R`VkV R`C`F RfJfV RjI`J RTFTSS[ RWJ]JXP RXQ]Q[WV[ RXSYW]Zd[l[',
	    'PoTGlG RXJkJ RdSjS RYWjW RYZjZ RYNY[ R_J_Q ReJeR RYNjNj[ R_CaF R]S[T RTGTSSZ',
	    'PoUGlG RXKkK RYRiR RYViV Rf[i[ RYOY[ RaHaZ RYOiOiZ R_C`F RdHhJ RUGTSS[',
	    'PoUGlG RWLkL RWSlS R_CaF ReLe[ R[H\\K R\\WX[ R]L]TZY RgHeL RUGTSS[ R]O\\W',
	    'PoUFlF RYIiI RVMkM RcQjQ R`C`F RaGaM R^N[PWR R^Q[UVW RcMcQ R]VbYl[ R\\TfTdW_YV[ RUFURSZ',
	    'PoUFlF RXKkK RaHa[ R[O^R RfOjS R`CaF RaRfXkY RfMdR R\\MYS RaR\\XXZ RUFURSZ',
	    'PoUGlG RYNjN RYRjR RWVlV RYJYR R`C`G RdSd[ RYJjJiR R`H`JaR[U RUGURSZ',
	    'PoUFlF RXKkK RWOlO RZVhV RZZhZ RZRZ[ R]H]O ReHeO RZRhRh[ R`CaF RUFURSZ',
	    'PoTFlF RWKlK RZShS RZWhW Ra[k[ R[N[X R`C`F RaMa[ RYPhOhW ReKjQ RlXk[ RTFTSS[ R_H^LXQ',
	    'PoUGlG RWMlM R\\SfS R\\I\\S R^V^[ R`C`G RfIfS RhVkY RcVd[ RYVW[ RUGUSSZ',
	    'PoTFlF RVMlM RYQiQ R[[`[ R`C`F R`G`[ RYJiJiQ RXS[T R`PcUfYkZ RjSdV R]WVY RTFTSS[',
	    'PoUFlF RVLlL RYOiO RYUjU RYXjX Re[j[ RYRY[ R`C`F RaGa[ RYIiIiO RYRjRj[ RUFTRS[',
	    'PoUFlF RVJlJ R\\NfN RZViV R\\H\\N R`C`F RfHfN RZQiQiU RZWV[ RaUeYk[ RUFUSR[ RZQYX',
	    'PoTFlF R[PfP RXSkS R`C`F RaQa[ RcLfO R`K[P RaSfXkZ RdHkL R\\IWK RaS\\XWZ RTFTSS[',
	    'PoUFlF RVLlL R\\QfQ R\\H\\Q RfHfQ ReUk[ R]W`Z R`CaF R[WW[ RfTcZ R[TYY RUFURSZ',
	    'PoUFlF R^JlJ RZO^O RZIZX R`N`V ReJdN R`NjNjV RhXk[ RfXjY R_DaF R^WYX RdVbY^[ RUFURTY ReQdW',
	    'PoUFlF RZLiL RZPiP R]WgW R[WdW Rf[j[ RXRX[ RZIZO R`C`F RaIaW RZIiIiO RXRjRj[ RfUgX RUFURSY',
	    'PoUFlF RXO^O RdOjO RXSjS RXJXS R`C`F RdJjJjS RaHaUeXk[ RaU]XW[ R^IXK RUFURSZ',
	    'PoUGlG RWKlK RYUjU RYZjZ RYPY[ R]H]N R`C`G RbPbZ RfHfN RYPjPj[ RUGURSY',
	    'PoUFlF RYUhU RXJXM R`CaF RaHaJ RXJkJkM RgMcP R\\MZP RaOjS R`L`Q R]VbYi[ R_QXS RfV`YX[ RUFURSZ',
	    'PoTFlF RXM`M RXR`R RXV`V Rf[k[ RXIXV R`C`F RXI`I`V RdJdU RkIk[ R^XaZ RZXV[ RTFTSS[',
	    'PoTFlF RVM`M RbOkO RbTkT RbZkZ R[H[[ R`C`F RbIb[ RbIkIk[ R[N`S R[LWW RTFTSS[',
	    'PoTFlF RYPiP RWTkT Rd[k[ R`C`F ReOe[ RkXk[ RhIdJ RXK\\M RkKfM R]O]W[YW[ RTFTSS[ RXI_IYPUQ RcHdLjP',
	    'PoTFlF RXM_M RiOlO RXQ_Q RaRjR RXIXY RXI_I_Q RcIiIiN R_CaF RcQdUhYlZ RiSeX`Z R^T_XWY RcIcN RTFTSS[',
	    'PoUFlF RZJiJ RWNlN RW[l[ RZUZ[ R_U_[ R`C`F RdUd[ RZUiUi[ RaHaN^PZRbSgQjR RUFTSS[',
	    'PoUFlF RYNiN RYRiR RcXjX RYJYR R`C`F RYJiJiR RaHbRb[lZk[ RiUjX RfTdX R\\VYYV[ R]R[X RUFURSZ',
	    'PoUFlF RYVjV RYZjZ RYJYP RYRY[ RbRbZ RYRjRj[ R`C`F ReMbQ RgPjPgP RfIdO R`HYI R^L_OXP RcIkIjP RUFURSZ',
	    'PoTFlF RWIkI RZNiN RZPiP RZSiS RZKZS R`C`F RZKiKiS R`I`K R^TWW R[VaYl[ R[VhVaYW[ RTFTSS[',
	    'PoTFlF RWJkJ RWQlQ RXUiU R^J^[ R`C`F RcJc[ RXNiNiT R[H]I ReGdI RcThYkZ R^TYYVZ RTFTSS[',
	    'PoTFlF RXNaN RXRaR RXJXZ R\\G\\J R`C`F RXJaJaR RdId[ R^U`WaYVY RTFTSS[ RdJmJhPlTlXhX',
	    'PoUFlF RYM^M RcMiM RYQ^Q RcQiQ RXTiT RYJYQ R`C`G RaHaT RcJiJiQ R^IYJ RgVbYY[ R[VbYl[ RcXV[ RUFURTY',
	    'PoUFlF RUMjM RZRlR Rf[i[ R\\W\\Z R`C`F RcGcM RUJjJjM ReVgX R`VaY RXWVZ R\\G[MYUkTj[ RUFURSY',
	    'PoTFlF RWKbK RWQcQ RYZaZ RYTY[ RYTaTaY ReIe[ R\\H^K R_DaF RZMZO R`M^Q ReJkJhPkTkXgX RTFTSS[',
	    'PoTFlF R^IkI R`PjP R`SjS R`VjV RXX]X RYIYX R]T]X R`L`V R`C`F ReIdL R`LjLjV RiXk[ R\\MYO RbX][ RTFTSS[',
	    'PoTFkF RWM_M RiOlO RWQ_Q RWJWQ RWJ_J_Q RXRXZ_Y_Z R`C`F RcIiIiO RlLlO RZGZJ RdUgYl[ RbRjRiUfY`[ R^TWV RcIbO RTFTSS[',
	    'PoTFkF RbIkI RWM_M RbQlQ RWQ_Q Rg[l[ RWIWQ RWI_I_Q RXRXZ_Y_Z R`C`F RhLh[ ReV`[ RZGZJ RgPdX RlXl[ RbLbQ R^TWU RhJfV RTFTSS[',
	    'PoUFlF RVIlI R\\LfL RYRiR RXUjU RYXiX RV[l[ RYOYR R\\G\\L R`C`F RaLaZ RfGfL RYOiOiR RUFTRS[',
	    'PoUFkF RXJ`J RWNaN RWQaQ R[[^[ R\\H\\Q R`C`F RfVl[ RgUa[ RdOhX RcLeU RcMkLgV RdHbP RZQYY RUFURSZ RZT_T_[',
	    'PoTFlF RWJbJ RXPaP RVWbW RY[\\[ RXLXP R\\G]J R\\U\\[ R`C`F RXLaLaP RdId[ RXRaR\\T RdIkIhPkTkXgX RTFTSS[',
	    'PoUFlF RWKlK RZMiM RYRjR RVUlU RYPYR RZHZM R`C`GaR RZHiHiM RYPjPjR R_R[W R[Xk[ RUFURSZ RgUcYW[',
	    'PoUFlF R^RjR R^UlU R]ZkZ RXIX[ R`C`F RgWk[ RaLfKlM RbU`Z R_OhOgR RdIeIaK\\M RXI\\IZO\\S\\VZW RUFURSZ',
	    'PoTFlF R`C`F RbHjHjO RgLcN RWH_H`OjS R\\JWK R\\MWO RfJaK RaOVT RaSZU RdUZX RfYY[ RTFTSS[',
	    'PoUFlF RWJkJ R_NcN ReZhZ RXPXS R[T[Z R_G_N RaPa[ RcGcN R[ThThY RXPjPjS R_DbF R[LXO RgGgNjMjN RUFURSZ R[G[M',
	    'PoTFlF RXJlJ RXRjR R[UaU R[S[[ R_J_Q ReJeQ RXMjMjQ R_C`F RaGbJ RdSdZkYkZ RjTdV RbZZ[ RTFTSS[ RXJXRWY',
	    'PoUFlF RYLiL RYOiO RXSkS RWVlV RYIYO R]P]V R`C`F RaIaO RePeV RYIiIiO RdXk[ R^XW[ RUFURSZ',
	    'PoTFlF RVJaJ RcMlM RWMaM RWT`T Re[i[ RWPWT RYVYY R\\H\\M R`C`F RWP`P`T RjHj[ RdPfU RTFTSS[ R^ZV[ R^V_YYZ',
	    'PoTFlF RYKiK RYMiM RXPjP RWRlR RdUkU RXWjW RV[l[ RYHYM R`C`F RaHaR RaUaZ RdRdU RYHiHiM R]TWV RTFTSS[',
	    'PoTFlF RYLjL RZQlQ R^WjW R^ZjZ R^T^[ R`C`F RaLaQ R]UjUj[ RYHjHjK RiMfP R[NfO^U[U RTFTSS[ RYHXRWY',
	    'PoTFlF RWKbK RYN_N RdPlP RYR_R RVUbU RYHYU R`H`U R`C`F RiPi[ R^W`Z RZWW[ RTFTSS[ RkHdIdTb[',
	    'PoUFkF RXM_M RWQ`Q R[M[Y R`C`F RhWl[ R\\H`L RdW`[ R\\HVM RXSXW RfLfRiY RcLkLjP R_S^W RcHaP R`XVZ RUFURSZ RfNeV',
	    'PoTFlF RWJaJ RXP`P RXS`S RVWaW Rg[k[ RXMXS R\\H\\M R\\S\\[ RXM`M`S R`C`F RdIkIk[ RfRhT RfMhO RTFTSS[ RdIcSbY',
	    'PoTFlF R][`[ RWNW[ RZQZX R\\H\\N R`C`F RWN`Na[ RgVl[ RgVb[ RWIXL ReQhY RiPfX R`H_L RdLgW RdMkLgV ReHbP R^QZQ R^Q^WZW RTFTSR[',
	    'PoTFlF RZKkK RVPlP RWUlU R[I[U R^W^[ R_J_U R`C`F RcJcU RhJhU RhWlZ RYXW[ R\\HWM RcWd[ RTFTSS[',
	    'PoTFlF RXR^R RY[\\[ RXO^N^R R`C`F RfSiQiS RaNfMlO RXJ[L RaVdYi[ RXI_I[MVO RjKfL R`UhUeY_[ RgHcIgNfR RaN`S RXRWV^U][ RTFTSR[',
	    'PoUFlF RVIkI R\\LfL RWOlO RYTjT RYWjW RYQYW R\\F\\L R`C`F RaOaW RfFfL RYQjQjW R^YV[ RdYl[ RUFUQSY',
	    'PoTElE RWHkH RYMiM RYPiP RWRlR RWZ_Z Rc[k[ RWUW[ RYKYO RWU_U_[ R`CaE RaGaT RcUcZ RYKiKjO RlXk[ RkUcW RTETSS[',
	    'PoUFlF R]KeK RYRiR RZWhW RZZhZ RYNYR RZUZ[ R`C`F RaNaR RZUhUh[ RYNiNiR R\\O]Q RfOdQ RbHlM R`HVM RUFURSZ',
	    'PoUFlF RaJkJ RYO_O RaQlQ RaVkV RZZ_Z RYIYTZ[ RYI_I_N RZS_S_Y RfQf[ R`DbF ReHfJ RcLdO RiLgQ RUFUSSZ RYNWX',
	    'PoTElE RYIjI RWPlP R]XdX RYZiZ RYSY[ R]U]X R`C`F RaGaP R]UdUdX RYSiSi[ R\\L^O RfLiO R\\IZO RfIdO RTETSR[',
	    'PoTFlF RXP`P RcSkS RXU`U RaWlW R\\[`[ R\\L\\T RWM`M`[ R`C`F RgPg[ RYI^I\\N RfHdN RdPbU RjNgN RbHkHjN RTFTSR[ RZGWLXUWZ',
	    'PoTElE RWIkI R]OeO RXTdT RVVlV R`C`F RaSa[ R]M]NeMeO RYKiKiQ RaVjZ RaV[YVZ RTETSS[ RYKYQiRbS',
	    'PoTElE RWIlI R]OeO RYQiQ RYSiS RWVlV R][a[ RYKYQ RaVa[ R]M]NeMeO RYKiKiQ R`C`F RfXk[ R\\XWZ RTETSS[',
	    'PoTFlF R`C`F R^IcIcN ReIjIjN RbL^P RhJeL RiLfO RaJ^L RfPlS RcQ]S ReS`U RgU`X RXM\\M RXR\\RWS RXI\\I\\[Y[ RiX_[ RTFTSS[ RXIXTV[',
	    'PoTElE RaHiH R\\RiR R\\UiU RYZlZ R\\W[[ R\\P\\U R`C`F R`W`[ RbPbU ReWe[ R\\PiPiU R\\WiWi[ RYJjJjL RTETRS[ RYJYRWZ R[LgL R^K^OiO RaGaJ',
	    'PoTFlF R`IkI RaKkK RWN^N R_NlN RaSkS RaUkU RaWkW R\\I\\[ RaPaW ReGeN RaPkPkW RZO^S R`C`F RhYl[ R[MVW RcY^[ R^HWJ RTFTSR[',
	    'PoUFlF RYK`K RcKjK RYM`M RcMjM RWTlT RYI`I`O RcIjIjO RcIcOiPkR R_CaF RdQaR RZQWR RYIYO_P`Q R_S[Xk[ RUFURSZ RgTcYW[',
	    'PoUFlF RbMlM RXQ`Q RcQkQ RXU`U RbUlU RYLYU RZWZZ R]M]U RWN`M`U RgMg[ RaVaY RaCbF R]W^Z RcHdK RjHhK RZHWN RZJ^J\\O RXWV[ RUFTSSZ',
	    'PoTFlF RaJlJ RXN^N RaRlR RXR^R RXIXX RVI^I^[ R`C`F RaMaR RdMdR RfGfM RhMhR RdUd[iZi[ RaMlMlR RdThU RjVlZ RaV`Z R^WUX RTFTSS[',
	    'PoTFlF RcLlL RcQkQ RcUkU RWW`W RcZlZ RW[`[ RXSX[ RWS`S`W R`C`F RdKd[ RhJhZ RZJ\\L RWJYL R^J`L RXHVJ R\\HZJ R_H]J RhHhM ReHaP R[S[W RWNWQ[Q RWN_N_Q[Q RTFTSR[',
	    'PoTFlF RWG_G RbGkG RXOjO RXUjU R[WaW RXIXM R[T[[ R]O]T RXI_I_M RcIcM RcIiIiM RXQjQjT R[K\\L ReKgL R`C_F RiVcX RaZZ[ RdOc[jZj[ RTFTSS[ RXOXUW[',
	    'PoTElE R`IkI RXK]K RXN]N RaPkP RVR]R R`SlS RVW[W RXHXQ RVTZTYZ RVH^H][ R`C`F RaLaP RdLdP RfGfL RhLhP RaLkLkP RdTgU RjWlZ RaW`Z RdVd[hZi[ RTETSR[ R[ZVZ',
	    'Po_ZlZ RZTTZ RSE\\EXITN RUN]MYW RUQWU[YeZ',
	    'Po][l[ RVVR[ R\\M`W RaLfV RgLkV RjDgM RXQTY R_D\\M ReDaM RSEYEVJSMYMVX RTQUUXX[YcZ',
	    'PodLlL R\\UlU R^J^U ReEeU RjD]F RRFYFTNZNXVR[ RUQVUZYd[m[ R',
	    'Po\\MlM R]VkV RdEdV RREYEWIUKRN RjD]F RTNZNWVS[ RTQUVXX[ZdZm[',
	    'Po]OjO R]VjV R^ZlZ R]H]W RcCcV R]HjHjW RWVR[ RYQUY RREXESNYMWW RTQVUXX\\YcZ',
	    'Po]NjN R]VjV R^ZlZ R]G]W R]GjGjW RWVR[ RRFXETM RYPUY RTNYNWW RcCcH RTQVUXX\\YcZ',
	    'PoZJlJ R\\MjM R\\QjQ RZUkU RcCcX R\\FjFjM RREXESNYNWVR[ RUQVUXX[Zc[m[',
	    'PoaQfQ R]VjV R]E]W RaJaQ RaJfJfQ R]EjEjW RREYETNYNWWR[ RTQUUXX[Zc[l[',
	    'Po[EkE RePkP R\\VkV R^[l[ R\\I\\W RfEfO R\\IkIkW RWVR[ RaEaN^Q RYQUY RREXETNYMWW RTQVUXX\\YcZ',
	    'PoaKfK RaPfP R\\VjV R^ZlZ R\\E\\W RaEaV RfEfV R\\EjEjW RWVR[ RYQUY RREXETNYMVW RTQVUXX\\YcZ',
	    'PoRNlN ReDe[ RWWS[ RYDYRUY RYLWV',
	    'PoRJlJ RXYfY RXCX[ RfCf[',
	    'PoSEkE RROlO ReEe[ RXVS[ RZEZPXW',
	    'PoSJjJ RRQlQ ReLe[ RfGkL R[DWJ RZKZQXVT[',
	    'PoUJhJ RUOjO RRTlT RUJUO ReQe[ RTEhEhJ RkLjO RYVWYR[ RYQYX',
	    'PoRFlF RSMjM RRSlS R_C_G ReOe[ R\\HXM RcJkM RYVVYR[ RYPYW',
	    'PoTEjE RUIiI RSMlM RRSlS R_E_M ReOe[ RZPYVWYR[',
	    'PoaOeO RRTkT RdQd[ RcDhH RXVT[ RZDVIfHlJ R]KZNUP RXGRJ RfIeO RXQXX',
	    'PoUFiF RRJlJ RRTlT R_C_J ReQe[ RUOhNjP R[KWO RdLhN RYVWZS[ RZQYX',
	    'PoYHeH RVQhQ RRUlU RVKVP ReRe[ RVKhKhP R`DjI RXXR[ R^DRJ RYRYX',
	    'PoRFlF R_OcO RRSlS R_C_F RcFcO RePe[ RhIkM RWISM R\\KXO RYVWYS[ R\\F[L RZPYW',
	    'PoTHYH R\\HlH RRKYK R]OkO RRUlU RTDTI RYCYP RdCdO RdRd[ RYWWYS[ RTKSQ RYRYX',
	    'PoRUlU RSHSQ RYCYP RdRd[ R]D\\G R\\K\\N RbGdKgOlP RVKVN RcC`J RbGkGhKeN`P RUDWG RTH^H^Q\\P RZSYWWZS[',
	    'PodKlK RaKiK R_KgK RYLaL R_OcWfZi[ RcEiG RlWk[ RSLXL^KbV R^D`S',
	    'PoRJlJ RRS_S ReWh[ RcRfZ ReDjF RlWk[i[ RbCaIcU',
	    'PoRIlI RTO_O RRY`Y ReWh[ ReDiE RlWk[h[ RbCbKdV',
	    'PoRIlI RSN^N RTS^S RRYaY RdWh[ ReDjF RkWk[h[ RbCaHdV',
	    'PoRIlI RSO`O RYOYY RfDiG R`WRZ RcDcKeYk[lW',
	    'PoSE^E RRKlK RTP^P RgDjH R_XRZ RcCcKeYk\\lW',
	    'Po]IlI R]MdM RSP\\P RXLX[ RaMaX RXR[V RXG[J RiDkG R[DTK RSEZH RXOTX RfW\\X RgCgMgPhZ RlWk[i[',
	    'Po^HlH R^NdN RSP\\P RXLX[ RaNaX RXG[K RZL[N RWP\\U RgEjG R[ETL RVSRW RTFYG RlWk[ RXOUU ReW]Y RfDfMhZ',
	    'PoULjL R`[h[ RSEjEjK RTLRRlRi[',
	    'PoTLjL RdYjY R_E_[ RREjEjK RTLRRlRkX',
	    'PoTK_K RV[][ RSD_D_K RlCl[ RSKRQ_Q][',
	    'PoWLgL R_YfY RR[l[ RUEgEgL RUQiQfY RVKTR',
	    'PoUMjM RfYkY RcCc[ RSGjGjM RZUWYR[ RTMSSlRkY R[C[PZV',
	    'PoSK[K RT[Y[ RRD[D[K RSKRQ[QZ[ RcE`Z R^[kX RhRlZ',
	    'Po^EkE RSK[K R]NlN R`[d[ RU[Y[ RRE[E[K ReEe[ RSKRQ[QZ[',
	    'PoTNkN RdYjY R_H_[ RRHkHkM RWDZG RgDcG RTMRRlRkY',
	    'PoSKYK R_[k[ RS[X[ RREYEYK R_F_Z RdCdU RlWk[ RSKRQYPYUX[ R\\NjHjTgT',
	    'PoSL]L RbLlL RV[[[ Rd[j[ RRE]E]K RaElElK RbKaRlQj[ RSKRR]Q\\[',
	    'PoSLZL R^PjP R_[k[ RT[Y[ RSDZDZK R_E_Z RdEdP R^EjEjQ RkVkZ RSKRQZQY[',
	    'PoSLZL RT[Y[ RSDZDZK RfVk[ RbU\\[ RdIdQhX RcQ`X R_IkIiN R`C]N RSKRQZQY[ RdLcU',
	    'PoUNkN ReYjY R_H_[ RTHkHkM RXDZH RfCeH R_R[VRZ RUMTSlRkY',
	    'PoSKYK RZNlN RR[W[ RREYEYK R_D_[ RfUlZ RjEcK RcNhW RdY]Z RSKRQYQXZ',
	    'Po\\DlD RSLZL R_SjS R\\ZlZ RT[Y[ RSDZDZK R_D_Z R_KjKjS RSKRQZQY[',
	    'PoSLZL R]KjK R\\QjQ RT[X[ RREZEZK R]E]K RdCdL RjEjK R_Q`TdXk[ RbM^O RjReWaY[[ RSKRQZQY[',
	    'Po`JlJ RSKXK RRQYQ RS[W[ Rd[h[ RSKRQ RRDXDXK R^H^[ RiCi[ R]JYN RbNdT R`C\\L RYRW[',
	    'PoSKYK R^MlM R\\[h[ RS[X[ RREYEYK R^F^W RlVkYiY RjD]F ReV[W RfEeKgV RSKRQYQYUX[',
	    'Po^HlH RSLZL RT[X[ R_[d[ RSDZDZK RdHd[ R`C[M R_O\\W RiNlW RSKRQZQY[',
	    'Po[HlH RSKYK RRQYQ RS[X[ RSKRR RRDYDYK RjWk[ RYRX[ RdDdH R^OcR RhM]ZjY',
	    'PoSKYK RS[W[ RREYEYK RbGbY RfTh[ RiDiNkZ RSKRQYPYUX[ R]F]Q[Z RjD\\F RgX]Y',
	    'Po^ElE RSL[L RfOkO R_ZlZ RT[Y[ RRE[E[K R_Q_[ R_QkQk[ RbJ^O RdEbL RlFkN RSKRQ[QY[',
	    'Po`EkE RRF^F RWShS Ra[h[ RWOhOhS RbEcIgMlN RUI]M RWCTJ R]HYLSO RjGfK_N RWSVWiVh[',
	    'PoSKYK R]QkQ R[ZlZ RT[X[ RREYEYK RdQdZ R]EiEcKlN RdJ[N RSKSQYRX[',
	    'Po]EkE RSKYK R]SkS R]ZlZ RS[X[ RREYEYK R]E][ ReEeK ReSeZ R]KkKkS RSKRQYQYUX[',
	    'Po^EkE RSKYK ReSjS R^ZlZ RS[X[ RREYEYK R^E^[ RbJbT ReSeZ ReEeKjKjS RSKRQYQYUX[',
	    'Po\\ElE R_JiJ RSLZL R_PiP RT[Y[ RREZEZK R_E_W RiEi[ RSKRQ[QY[ RdV]W RgV`W RkUcV',
	    'PoXHeH RRKkK RXSfS Ra[g[ RXOfOfR RdDgEjH RXDTI RbIhOlP RXRVViVg[ R^C[JVORQ',
	    'PoRFlF R_McM RVShS Ra[h[ R_C_F RcFcM RWOhOhS RhIlM RVHSL RVRTVjVh[ R\\F[KXMSO',
	    'PoSKYK R^PjP RRQYQ R^UjU RS[X[ Rf[j[ RREYEYK R^L^[ RdCdK R^LjLj[ RhElJ R_F\\J RSKRQ RYRX[',
	    'PoTK]K RbKlK RaPlP RV[[[ Rd[j[ RSD]D]J RaDlDlJ RVRXT RcRfT RbJ`Q RZVRX RhU`X RlQk[ RTJSP^O\\[',
	    'Po^JkJ RSKZK RRQZQ R^QlQ R^UlU Rg[l[ RS[X[ RSKSQ RREZEZK R^M^[ R^D^J R^DkDkJ R^MlMl[ RYRX[',
	    'Po]ElE RSKYK RRQYQ R]SkS R[[l[ RS[X[ RSKSQ RREYEYK RdSdZ R]K_P RbLeO RgLkP RdGbL RjGhL R_G]L RYRX[',
	    'Po[FlF RSKYK R]NiN RRQYQ R[VlV RS[X[ R_[c[ RSKRQ RRDYDYK R]I]N RcCcF RcTc[ R]IiIiN R]QiQcT RYRX[',
	    'Po^DkD R^HiH RSLZL R^LiL R\\PlP RT[Y[ RREZEZK R^D^Z RkSfU RfY\\Z RSKRRZQY[ RdQfVj[',
	    'Po[GlG RSLZL R^QjQ RT[X[ R`[d[ RREZEZK R^K^Q RdCdG RdQd[ R^KjKjQ RhUkX R^V[Y RSKRRZQX[',
	    'PoSKYK R]TjT RS[X[ RREYEYK R]N]T R]NjNjT RiWm[ RgFkJ RSKRQYPYUX[ R\\[jY RaD^JjJ RdJdZ',
	    'PoSKYK R\\KbK RfKlK RRQYQ R\\QbQ RfQlQ RS[W[ R^[b[ Rh[l[ RSKRQ RRDYDYK R\\DbDb[ RfDlDl[ RYQW[ R\\D\\R[Y RfDfTd[',
	    'PoRKYK R]MkM RRQYQ R]RkR R[VlV RS[X[ RRKRQ RREYEYK R]I]R RdId[ R]IkIkR R_DaG RhDfI RYRX[',
	    'Po^JjJ RSLZL R]TjT RbZkZ R_ZgZ R\\ZeZ RT[Y[ RREZEZK R]N]T R^E^J RdJdZ R^EjEjJ R]NjNjT RhVlZ RSKRQZQY[',
	    'PoZEdE RSKXK RfKlK ReQlQ R[XcX Rg[k[ RREXEXK R[J[[ R[JcJcZ RfKeR ReElElK RW[T[ R_E_K`Q RlRk[ RSKRRYQYVX[ R_I^R',
	    'PoZEdE RSKXK RfKlK R[QcQ R[YcY RR[W[ Rf[k[ RRDXDXK R[K[Z R[KcKcZ ReDlDlK R_E^K RfKeQlQlUk[ RSKRQXQXTW[',
	    'Po[GkG RSKYK RbKhK R^OkO R[SkS RS[X[ RREYEYK R^D^O RbCbK RcOc[ RhDhK RcRiXlY RcR]YZZ RSKRQYQYUX[',
	    'PoSKYK R]NkN RRQYQ R]RkR R[VlV RS[X[ RREYEYK R]I]R RdId[ R]IkIkR RjDiGfI R]D_H RcCcG RSKRR RYRX[',
	    'PoUIiI RVLhL RRNlN RXUfU R`[f[ RSESH R_C_F ReGeO RWQgQfT RSEkElH RePlS RXTWWhWf[ RZGZOYQRS',
	    'PoRF^F RSI]I RiMlM RUN\\N RTT\\T RXCXI RTQ\\Q\\T RSKRM^K^N RbEiEiL RlJlL RbQcUhYl[ R`PjPhTdX^[ RTTSW\\W\\[X[ RbFaM',
	    'PoSKYK R^LeL ReNiN R[PlP RRQYQ R^XeX RS[X[ Rd[i[ RRDYDYK R^S^Y R^G^L R^GeGeL R^SeSeX R\\DjDiN RiPi[ RSKRR RYRX[',
	    'PoSIkI R^NjN RSPYP R[SlS RS[X[ RRLYLYP R[C[I RbCbI RUEXG RgFdI RcReWgYl[ R_K\\Q RcU_YZ[ RRPRUYUX[ RcObV',
	    'Po\\HkH RSKXK R[PlP RRQXQ R[SkS RZWlW RS[W[ RSKSQ RRDXDYK R^L^S RcDc[ R\\D\\LjLhS R\\DkDkL RXRW[',
	    'PoZElE RSKXK RaMhM RRQYQ R_WcW RfWkW R][k[ RS[W[ RSKRQ RRDXDXK R]E]Z R_P_W RaHaM R_PcPcW RfPfW RaHhHhM RfPkPkW RYRW[',
	    'PoZFlF RSKXK RgQkQ RS[W[ Rd[j[ RREXEXK R\\I\\Q ReIeO RkHkQ R^D_E RhCgE R^KaK R^NaN R\\IbIbQ_Q RcT`YZ[ R[TkTj[ RSKRQXPXVW[',
	    'PoWThT Ra[h[ RTFTO RYCYO RRG_G_O RVQhQhT R\\D_G RVDRG R\\J\\M RcFdKhNlP RWTVWjWh[ RVJVM RcC`J RbGjFhKeM`O',
	    'PoZHlH RSKXK R\\LjL RfQjQ R[TjT RfVjV RR[V[ RRDXDXK R[N[W`V`[ RcCc[ RfVf[ R\\EjEjL RjNj[ RfNfQ RSKRQXPXVW[ R[QZ[ R_N_Q[Q',
	    'Po\\IbI ReIkI RSKYK R]PjP R]SjS R[WlW RS[X[ RREYEYK R\\D\\I R]L]S R\\DbDbI RcLc[ ReDeI R]LjLjS ReDkDkI RSKRQYQYUX[',
	    'Po]FlF R\\JlJ RSKYK R^RjR R^VjV RS[X[ RREYEYK R^N^V R`C`J RdJdV RhChJ R^NjNkV RgXl[ RaX[[ RSKSQYQYVX[',
	    'Po[DlD R]JjJ RSKYK R]MjM R[OlO R]UjU R]XjX RZ[l[ RS[X[ RREYEYK R]R]X R]G]M RcGcM RcRcX R]GjGjM R]RjRjX RSKRQYQYUX[',
	    'Po\\IkI RSKYK R[LlL RRQYQ R]SiS RS[W[ RSKSQ RREYEYK R\\D\\I R]O]S R^V^[ RaDaI RfDfI R]OjOjS R\\DkDkI RdSeVhYl[ RbT[X RkUfW RdY]Z RYRX[',
	    'Po[DlD RSKXK RR[W[ Rg[k[ RRDXDXK R[K[[ RcDc[ R[KkKk[ R^NaQ R^TaW RfUiW RfNiQ RfFkH R`N]R R`U]X RhNdR RhTdX R_F[I RSKRQXQXVW[',
	    'Po[ElE R[HlH RSKXK RaLgL R\\NlN RRQXQ R^TjT R^WjW RREXEXK R^Q^W RaEaK RdCcF RdNdW RgEgK R^QjQjW RgYl[ RaY\\[ RV[S[ RSKRR RXRW[ R[E[RZZ',
	    'Po\\DaD R[FcF R\\IbI R\\KbK RRKZK R\\PaP RWUhU Ra[h[ RVKVQ R\\M\\P R\\MaMaP RhKhQ RURhRhU RSFUI RdFgI RcKjJlK RjFdK ReMdP RVDSF RXFSK RgDeF RYMYO RkMlP RWUUXiXh[ RSMRP',
	    'Po[HbH RdHkH R[JbJ RdJkJ RSKXK R]OjO RRQXQ R]QjQ R]SkS R[VjV RS[W[ RSKSQ RRDXDYK R[D[J R^L^T R[DbDbJ RdLdS RdDdJ RdDkDkJ R^LZP RdFgFkF R\\F_F[F R^FaF^F RhFdF R]VbYl[ RiWdY[[ RXRW[',
	    'PoVOfO RR[l[ RTFgFgY',
	    'PoYIgI RR[l[ RfMdY RYDWTfRd[',
	    'Po_OlO R]YlY RRGRS R^FlFlZ RXVS[ RYCYQXW',
	    'PoTSlS RRZlZ R_C_M RSMlMl[ RREXJ RkFgJ',
	    'PoWKfK RRNlN RZ[_[ R_N_[ R_NaSeWlZ RTQYS RiQfRbT RYCWL RXFgFeN R[VRY',
	    'PoUIgI RRNlN RZ[_[ R_N_[ RTEhEhN R_N`ReWlZ RUQYS RgRbT R_USX',
	    'PoWJeJ RRLlL R[[_[ R`MbSfWlY RYCWJ RjObR RXFfFdL R\\N`RaVaZ R`L\\NRQ R^Q[SRU R`TYXRY',
	    'PoSF]F R`FkF RTI]I R`IkI R_MlM RRVlV RUZhZ RXCXO ReCeO RUQhQh[ RTN]M',
	    'PoWHfH RRLlL R\\PcP RURYR RZTcT Re[l[ RUMUZ RfMfZ RlXl[ R_P_VbY R^W[[ RYCWI R\\M[S R_T]Z RkPeS RYFfEdL RZYRZ',
	    'PoXGfG RRJlJ RWQgQ RWTgT RRVlV RSLSO RWNWT R_N_[ RWNgNgT RSLjLjO RYCWG R_VeYl[ RXEfEeI R_VYYR[',
	    'PoWGfG RRJlJ RWQgQ RWTgT RRVlV RSLSO RWNWT R_N_[ RVDfDfI RWNgNgT RSLjLjO R_VeYl[ R_VYYR[',
	    'PoRGlG RVJgJ RRO]O RRVlV RXKXS RdTd[ RVDhDhI RTLUM RdLhLkO RbL^O RdPaS RXNTR R\\KZM RVO\\Q RYYT[ RaOiOhSfS RZTYY',
	    'PoXGfG RRJlJ RRO]O ReShS RRWlW RXKXS RdTd[ RaOiOhS RTLUM RdLhLkO RaL^O RcP`S RXNSS R\\LZM RVO\\Q RXCXH RYTXYS[ RYEfEeJ',
	    'PoRJlJ RRO]O RRVlV RXKXS RdTd[ RTLUM R_PjOkO RWHeHaM RXNSS R\\KZM R`LdN RhQlS RVO\\R RYCXH RgKeNaO RbQ^S RXYS[ RXEfEeJ RYTYY RePdW',
	    'PoWGgG RRJlJ RSO]O RRVlV RXKXS ReTe[ RfPfS RVDgDgJ RTLVM R`PiQkT RhNkP RcKaM RbQ_T RXNSS R\\K[M RXO]R RYUYYT[ RfM`M',
	    'Po_ElE RTI\\I R`JlJ RaMkM RSM\\M RaOkO RRR^R RaRlR RW[[[ R[O[[ RSE\\E\\M RaIaS RcCcG RfJfR RhChG RUTWV RaI^L RaTdXk[ R`TjTgXcZ^[',
	    'Po^ElE RRH^H R`KkK RTK\\K R`MjM R`OjO RYQ]Q R`RlR RRU^U RYMYQ RTE\\E\\J RYM]M]Q RaIaR RcCcG RfIfR RhChG RTVWX RaI^L RaUdXj[ RWQRR RRMVMUR R[S[[W[ R_TjTgXcZ^[',
	    'PofDSJ RhKcNUR RlRcW\\YR[',
	    'PoRE`E RRNaN R]E][ RiDfGbI RjKgObQ RlSgXa[ RVEVPUUS[',
	    'Pob[l[ RYCYH RZTS[ R]MYV RiDeHaJ RjJgM`P RkPhS`W RRI_I[T RUIWPYU\\X`Zg[',
	    'PoUP^P RRQ[Q R\\[`[ RUE`E`[ RXH\\L RjDgGcI RlSfYb[ RUEURTY RkKgObP]P',
	    'PoTFkF RSMlM R_C_F RYGfK RfFaIXK RgR[V RkUeX\\Z RdOYQ RfXX[ RUMUSTY',
	    'PoTFkF RULlL R_C_F RYH[K ReGdK RdNYQ RgR`TYU RULUSTY RkUdYX[',
	    'PoRHlH RTS\\S RTMTS RTM\\M\\S ReWi[ RiNcRgZ ReDjF ReL]P ReR^U RkRfW][ RbLeX RlXk[i[ RbCbIdU R\\WRY',
	    'PoREaE R][`[ RSIS[ R[I[[ R[I`I`[ RjDeH RhOcQ RkKfP RUNVT R]N^T RlSfYc[ RSIXIW[X[V[',
	    'PoSPaP RZMZ[ RZP_V RZOXSRY RUHVL RYG[K R_G]L RjDgGcI RkKhOcQ RlSgXa[ R`DUE',
	    'PoZE`E RYP`P R][k[ RZJZP RZCZI R]S]Z RlWkZ RTIaI`K RiEcH RiLcO RkQhTaW RXSVZ RTITSR[ R^LVM',
	    'PoVI_I RVM_M RWV^V R\\[a[ RWQWX R[F[M RWQ^Q^V RTDaDa[ RkKfP RkTeZ RjDgGcH RTDTTR[',
	    'PoSJZJ R\\JcJ RWCW[ R_C_[ RkDdK RlLeS RXMYP RlTgYb[ RVHSU R_I[U RaMcP',
	    'PoRFaF RSJ`J RTS_S RTNTS RZCZJ RTN_N_S RgGcI RjDfH R]T[Y RkKgOcQ RlTgXa[ RVTVX R_YRZ RaXUZ',
	    'PoUZ_Z RSFSI RUSU[ RUS_S_[ RSF`FaI R\\N`R RZL]O R\\I`M RYCZF RiDeI RWISM RjKgOcQ RlTgYb[ RZLTRRS',
	    'PoSEaE RTM`M RTP`P RRSaS RV[Y[ RTHTM RXEXM RZSZ[ R\\E\\M RTH`H`M R]V`Y RVVRY RiDfHcI RjKgOcQ RlTgXb[',
	    'PoSFaF RRJbJ RTP`P RTS`S RRWbW RTMTS RZCZF RZSZ[ RTM`M`S RjDfHcI RWGWJ R^G]J RlTgXb[ RjKgPdQ',
	    'PoTG`G RTK`K RRNbN RTU`U RW[Z[ RTDTJ RTQTU RZKZN RZUZ[ RTQ`Q`U RTD`D`J R^WaY RUWRZ RiDfHcI RjKgOcQ RlTgXb[',
	    'PoSEYE R[EbE RTMbM RTTaT RVWZW R\\[a[ RSGSJ RVTV[ RVHVJ RXMXS RSGYGYJ R\\G\\J R]M][ R_H^J RTPaPaT R\\GbGbJ RjDeI RkKeQ RaV\\W RlTfZc[ RZZU[ RTMTUR[',
	    'PoaOa[ RfESK RiK`PSS R_P`W',
	    'Pod[i[ RXLX[ R_NdR RYCSI RZHRP R`C\\L R^HlHlQj[',
	    'Po^[k[ RWLW[ R^F^Z RdDdU RXCSI RYJRP RlWkZ RkIYN RkIjSgT',
	    'PoWLW[ RYCSJ RYJRQ R_E_NcUgYl[ R]RY[ R^MjMgTbX][ R[EhEgM R^J\\V',
	    'Po[HlH RXLX[ RcCcH RYCSI RZIRQ RaHaP_U[[ RaOjOi[d[',
	    'PocYkY R^YfY R[ZcZ RXLX[ RYCSI RZIRP RhTk[ RaEgDkN R_F[O RbM^Y',
	    'Po\\PiP RXLX[ RYCSI RZIRP R^P_TcXj[ RhQcW[[ R^E^K[M R^EgEhMlMlJ',
	    'Po[EkE R[OkO RYZlZ RXLX[ RcEcZ RYCSI RZIRP',
	    'Pof[j[ RWLW[ R]D]V RdV^[ RXCSH RXJRP ReQbY RbS\\U RaJYK RbElElUk[ RfEfMdV',
	    'PoWLW[ RcCcN RXCSI RXJRP R_NaTeXl[ R\\HkHjL R\\NjNgTcX\\[ R\\H\\R[Y',
	    'Po\\MkM R[ZgZ RXLX[ R\\F\\W RYCSI RZIRP RdUZW RlVkZiY ReFeKfV RjD\\F',
	    'Po^LhL ReYiY RXLX[ RaPa[ R[PjPjX RcDjK RXCSI RYIRP RbDZL',
	    'Po\\MkM RhYkY RWLW[ RfCf[ R[GkGkM R_V[[ RXCSI RYIRP R\\M[SlRkY RaCaQ_W',
	    'Po\\IkI R\\QkQ RZZlZ RXLX[ RcIcZ RYCSI RZIRQ R`DfF',
	    'Po\\FkF RdOkO RZZlZ RWLW[ R]L]Z RdFdZ RXCSI RYJRQ',
	    'Po^LiL R^SiS RZZlZ RWLW[ R^E^Z R^EiEiZ RXCSI RYJRQ',
	    'Po]JkJ R\\QkQ RZZlZ RXLX[ RcCcZ RXCSI RYJRP R_E[N',
	    'Po\\TjT RZZlZ RXLX[ RcOcZ RYCSI RZIRP R^GcLkO R[EjEdK[P',
	    'Po\\GjG R[MlM R[QlQ Rc[g[ RXLX[ RcCcL RhMh[ R^TaW RXCSI RYJRQ',
	    'Po[ElE RfRkR R\\ZkZ RWLW[ R\\K\\[ RaEaQ RfEfR R\\KkKk[ RYCSJ RYJRQ',
	    'Po^QfQ R^VfV Rf[j[ RXLX[ R^L^X R^LfLfV RYCSI RYJRQ R]JZL R`C\\J R^HlHlUk[',
	    'Po]JjJ R]OjO RXKX[ R]D]Z R]DjDjO ReUj[ RYCSI RZIRP RcOfW RjSeU ReYZZ',
	    'Po\\IlI R\\NkN R[UlU RXLX[ RdId[ RYCSI RZIRP R_C`G RhCgG',
	    'Po`TgT R[YlY RWLW[ R[E[[ R`K`T R`KgKgT R[ElEl[ RXCSI RYIRP',
	    'Po[KlK R\\OjO R\\RjR RZWlW RXLX[ RcCc[ R\\GjGjO RXCSI RYJRQ',
	    'PoZNkN RXKX[ R]HaK RgJlO RbN[U RXCSI RYIRP RaD]H RgF^N R^RcWk[ R_RiRdW_YY[',
	    'Po\\HjH R[NlN R[TkT RYZlZ RXLX[ RcCcN RcOcZ RYCSI RZIRP',
	    'Po\\KjK R[OkO R[TkT RYZlZ RXLX[ R\\D\\K RcOcZ R\\DjDjK RXCSI RYJRQ',
	    'PocGjG RZLlL R`UcU RXKX[ R^F^L RcCcU R^PZT RXCSI RZIRP RjOhTeX^[',
	    'Po^LiL R[QlQ R_[c[ RXKX[ RcLc[ RgTkW RcClL RXCSI RZIRP RcCZL R]T[Y',
	    'Po[ElE R\\SkS RY[l[ RXLX[ RcSc[ R[L_O RaLeP RfLkP RjHfK RXCSI RZIRP R^H[K RdHaK',
	    'Po\\GjG RZMlM RcSjS RXKX[ RdCdZ RXCSI RYIRP R]Q[Z R]UaZi[m[',
	    'Po\\JlJ RcQkQ RXKX[ RdJdZ RXCSI RZIRP R^D`H RiCfH R]O[Y R]S^Wc[m[',
	    'PoZFlF RbZlZ RWLW[ R_C_I RfCfI R\\WYZ RbU^W R[MiMaV RXCRI RYJRP RbIcL',
	    'Po[GkG RZPlP RWKW[ RbCb[ RbPjX RcP[X RWCRI RWLRP R\\I^M RhIfM',
	    'Po]LiL R[PkP R`[c[ RWLW[ R[G[J RcCcG RcPc[ R[GkGlJ RhTkW R]UZY RXCSI RYJRP',
	    'Po]HjH R]LjL R[OlO R[SlS Rc[g[ RXLX[ R]D]L RgOg[ R]DjDjL R^UaX RXCSI RYJRQ',
	    'Po[H`H RfHlH R[M`M RfMkM RfSlS RWLW[ RfCf[ RXCSI RYJRP RcRZT RaDaQ_W[[',
	    'PocGkG RZMlM RcRkR Rb[l[ RXKX[ R^F^M RdCdZ R^VaZ RXCSI RZIRP R]S_Y R]O[Y',
	    'Po\\GkG R]RkR RXLX[ R]J]R RdCd[ R]JkJkR RdQlY RbS\\Y RXCSI RYJRQ R`LaP RgLfP',
	    'Po[FkF RZNlN R\\WdW Rd[i[ RWLW[ R\\R\\Y R\\RdRdW RiNi[ RYCSJ RYJRQ RbHkL RbH`K[M RcCaJ',
	    'Po`VgV Rg[k[ RXKX[ R\\K\\[ R`O`W RdCdK R`OgOgV R\\KkKk[ RhElI RXCSI RZIRP R_E[I',
	    'Po[FlF R_NjN R_RjR R_VjV R[ZlZ RWLW[ R[L[[ R_J_V R_JjJjV RXCSI RYJRP RdCcJ',
	    'PodSkS RXKX[ RdNd[ R^GbK RfGlL RXCSI RZIRP R`D\\L RhDdK R^P\\X R^TaZd[l[',
	    'Po`JiJ R`NjN RaWhW Rh[l[ RXLX[ RaRaX RdGdN RaRhRhW R]ElEl[ RYCTI RZJRQ R]E]Q\\X',
	    'Po[GkG RWLW[ RcCc[ R]L`O RcPkY RgMkP RcPZY RXCSI RYJRP RhJeP R^J[Q',
	    'Po[GdG RZMdM R_RdR RiWlW RWKW[ R[P[Y R_F_Y RfEf[ RfElElW RWCRH RXHVLRP R\\CZJ RdXYY',
	    'PoZFlF RZMlM R\\QjQ RcUjU Ra[l[ RWLW[ RcCcZ R\\JjJjQ RXCSI RYJRP R\\SZZ R\\V_Zg[',
	    'PoZGkG RYLlL R\\UiU R\\ZiZ RWLW[ R\\P\\[ R_C_L RfCfL R\\PiPi[ RYCRJ RYKRQ',
	    'Po^JlJ R`MjM R`QkQ R_UkU RaZlZ RWLW[ ReCeX R`FjFjM RXCSH RWKRP RXHVN R]SYZ RYE]FZN^M[X RYQ[V_YfZ',
	    'Po]HjH R]LjL RZOlO RcTjT Rb[l[ RWLW[ R]D]L RcOcZ R]DjDjL R]T_WaZ RYCSJ RYJRQ R]TZ[',
	    'PoZLaL RdLkL RZPaP RZUaU RWKW[ RZEZ[ RZEaEaL RdEkEkL RfVk[ RWCRH RWHVLRP RdQgX RcPkPiVeYa[',
	    'Po]EdE R]MlM R]UlU Ri[l[ RXKX[ R]E]R][ RbPb[ RgPg[ R]HlHlM R\\PlPl[ RYCSI RZIRP R\\M[X RjDbE',
	    'Po\\JjJ R\\NjN R[RkR R\\VjV RZ[l[ RWLW[ R\\F\\N RcRcZ R\\FjFjN RXCSI RXJRQ RbCbF',
	    'Po^GlG R^MiM R^PiP RXKX[ R^I^P R^IjIjP RaQ[W RXCSI RYIRP R_C\\J R_TcXgZl[ R_TiTfWbYZ[',
	    'Po\\JlJ R_RkR R_VkV R_ZkZ RXKX[ R_N_[ ReEeN R_NkNk[ RXCSI RYIRP R\\E\\R[Z RjD\\F',
	    'Po[JlJ R]PiP R]SkS RYXlX RXLX[ RdPd[ R]LiLiP RXCSI RZIRP R\\FiFiJ R]L]P RaCaJ R]S\\X',
	    'Po[FlF RZPlP Rd[h[ RWLW[ RZKZN RcCcF RcMcP RZKlKlN R`W\\Z RXCSI RYIRP R_G_J RhGgK RaQ_X R`UiTh[',
	    'Po^OjO RZSlS R]ZjZ RXKX[ R]U][ RdOdZ RjUj[ R]H`IbL RXCSI RZIRP R`GfI R_M]R RbE[H RaFjFfJaMZN',
	    'PoZKaK RWKW[ RZFZK R]C]K RaFaK RWCRH RWHVLRP ReCbN R[QZX RdIlI RjIhSfWc[ RdJgTiWl[ RYPaP R_P_XcV',
	    'Po[RkR R[UlU RXKX[ RhNlR RhFlI RXCSI RYIRP RfKaP RcFcJ RaI^LaN RdVgYl[ R^E[K RbV`Y[[ RcRbX RiD_E',
	    'Po[EkE RcIjI RZNlN R[QkQ RcUjU RZZlZ RXLX[ R]H]N R]T]Z RcEcN RcQcZ RXCSI RZIRP',
	    'PoZElE R[NkN R\\QjQ RZTlT R_[c[ RWLW[ R[H[N RaEaN RcTc[ ReEeN R[HkHkN RhWkZ R]WZZ RXCSI RYJRP',
	    'Po^KbK R^TkT R\\WlW RWLW[ RdJdP RdQd[ R[EkEkI RXCSH RWKRP RfNkN R_Q]U R[IkJgK RbN]N R[E[RYZ',
	    'Po[FkF R\\QkQ RXKX[ R\\K\\Q RaKaQ RcCcK ReKeQ R`U`[g[hZ R\\KkKkQ R`SdU RhUlY RXCSI RYIRP R\\VZZ',
	    'PoZKbK RZObO RZSbS RWKW[ RZEZK R^C^K R^O^X RbEbK RWCRH RWHVLRP RaWYX ReIlI RgDdL RkIhTb[ RdKgUl[',
	    'PoZJbJ RYMcM RWKW[ RZEZJ R^C^J RbEbJ RWCRH RWIVLRP RfCdO RaRZR R^N^W RYWbV ReJlJ RkJgUb[ RdMgUiXl[',
	    'PoVKV[ RZKZ[ R^C^[ RWCRH RULRP RZE[J RgRkZ RiQeZ RWHUN RaE`I RfNiX RjLgX R`N`U RfJhW RfIkIhV R\\N[U RfCdN RZKbKb[`[',
	    'PoZFlF R\\OkO RZRlR R_[g[ RWLW[ R\\I\\O R`V`[ RaIaO RcCbI RfIfO RgXg[ R\\IkIkO RhVlZ RXCSI RXKRQ RaTeU R\\VZ[',
	    'Po[FkF RZJlJ R\\PjP R\\TjT R[WjW RYZlZ RXLX[ R\\M\\T RcCcF RcMcZ R\\MjMjT RXCSI RZIRP R_G_J RgGfJ',
	    'PoYFcF RYLbL RZRaR RZVaV R_[a[ RVKV[ RZNZ[ RZNaNa[ RWCRH R`IbM R^E[L RXIVLRP RfIlI RjIgTfXc[ ReKgTiXm[ RgDdL',
	    'Po\\FjF R[IkI RZMaM RdMkM RcQlQ RZTlT Re[l[ RWLW[ R^K^Q RcCcI RfTf[ RgKgQ RlXl[ RXCSI RXJRP R`T^YZ[ RaPZQ',
	    'Po_IfI R\\QiQ R]WhW R]ZhZ RWKW[ R\\K\\Q R]S][ RcKcQ R]ShSh[ R\\KjKjQ RWCSH RWKRP R_M_O RcCfFlI RfMeO RbC^GYI',
	    'PoZIbI RZMbM RXQcQ RVKV[ RZFZM R]C]F RZFbFbM RhWl[ RgWc[ RWDRH RVLRP RfRjY RiQeY RWHUM R^M^Q ReMhX R`[^[ RfCdLgV ReIkIhV R\\Q[Y R\\UaUa[',
	    'PoZIbI RYLbL RYTcT RVKV[ RZEZI R^T^[ R^C^I RbEbI R`RcU RZO]R RaVbY R`O[T RWDRH R]MZP R[VYZ RfDcN RXHVLRP ReIlI RjIgUfWc[ ReKgSjZ',
	    'PoZJbJ RYNcN RZPbP RZSbS RZZbZ RWKW[ RZVZ[ RZEZJ R^C^J RZVbVbZ RbEbJ R]K_M RWDRH RWIVLRP RhVc[ RfQjY RiPeY ReLhX RfCdKgV ReIkIhV',
	    'PodFlF RXFbF RXLbL RdMkM RdQjQ RZR`R RdTjT RZV`V RdWjW R][`[ RVKV[ RZOZ[ RZHZK R\\C]F RZO`O`[ RdCdIkIkM RdMd[lZl[ RWDRH RVLRP RWIUN R`H^L',
	    'PoZFlF RdLjL R[NkN R\\QjQ RYTlT RWKW[ R^W][ R[L`L_T R[HaHaL RdHeLfT RdHjHjL RbCcF RWCSH RWKRP RcUfYl[ RcTZX RjVeX R[H[L RcZ]Z',
	    'Po\\GjG R\\IjI R\\MiM R[PjP RYRlR R`[c[ RWLW[ R\\D\\I RcKcP RcUc[ R\\DjDjI RgUeV RXCSI RXIWLRP RgTlV ReXiZ RaQ]U_UYV R`Y[Z',
	    'Po[GaG RdGkG R[JaJ RdJkJ R[LaL RdLkL R\\QkQ R\\SjS R\\WjW R\\ZlZ RWKW[ R[D[L R]O][ R[DaDaL RdOdZ RdDdL RdDkDkL RWCSH RXIVLRP R\\PYT',
	    'Po[[e[ R[K[[ RhMlW R[DdH RUNSW RfUf[',
	    'Po[D[[ R^HbL RUISO',
	    'Po[[e[ R[J[[ RfVf[ RhMlU RUMSU R[D^FaI RhEfK[URY',
	    'Po]ZkZ RWCW[ R\\FkF_S]T\\X RYHZL RlTkZ RSISO',
	    'Poe[j[ RWCW[ R`V[[ RYH[L RbQ^Y RSHRO R\\JlJlTk[ RcCcLaU',
	    'PoUFhF RSMkM RZ[e[ RZRZ[ R]OaS RjUkY RgSkW RfXe[ RVSSY',
	    'Pod[i[ RWCW[ R_VZ[ RYH[L RaP^X RSIRO R\\FlFlRjZ RcFaT',
	    'Po[FlF R_[d[ RWCW[ ReFe[ RYH[L RSIRO',
	    'Po]OjO R^[k[ RWCW[ R^O^Z R]EjEjP RYH[L RlVk[ RSIRO',
	    'PoUJhJ RVPiP RZ[e[ RVJVO R[T[[ RTEhEhJ R\\R`T RgTlY RjMiP ReXe[ RVTSY',
	    'PocPhP RZ[e[ RZSZ[ RgTlY RfXe[ R\\RaT RVTTY R^F\\LYOSQ RVIaL RTEiEiJhP',
	    'PoSKkK RZ[e[ R[T[[ RUF`F`P R]RaT RgTlY ReXe[ RUTSZ RhD^F',
	    'Po]NlN RWCW[ ReEe[ RYHZL RSISO RjD^F',
	    'Po^HiH RRNlN RZ[f[ RZSZ[ R^C^N R^PaT RgSlX RfXf[ RVTSZ',
	    'PoSEkE RZ[e[ R[S[[ R_E_O R]PaT RgSlX ReXe[ RVTSY R_HiK',
	    'PoRIkI RXZ^Z RXQXZ R_W^Z RfDiG R`SbX RXL]O RgZj[ RlVk[ RTRRX RcCcKeW',
	    'Po\\FkF R[NlN RWCW[ RdFd[ RYH[L RSHRO',
	    'Po^HlH R][k[ RWCW[ ReP\\Y R^NhMcR RlWk[ RYGZJ R`C\\L RSHRO',
	    'Po]KlK RWCW[ RhWl[ RaU[[ RYG[K RePiY RcP_X RdCdLgV RSIRO RdKaV RdIbT',
	    'Po]JlJ Rb[g[ RWCW[ RgCg[ RYHZL RaOcT RSISO',
	    'PoRHlH RTOjO RZ[e[ R[T[[ R_C_N R\\QaS RgTlY ReXe[ RVTSZ',
	    'PoSGkG RWOiO RZ[e[ RWGWN R[T[[ R_C_G R\\Q`S RgTlY ReXe[ RVTSY',
	    'Po[IlI R_ZkZ RWCW[ R_I_Y RdCdI RYH[L RSHRO',
	    'Po^[k[ RWCW[ R^F^Z RdCdU RlVkZ RYHZK RkIjTfT RSIRO RkIZN',
	    'Poh[l[ RVCV[ R]EhEhZ R`LdQ RYHYL RlVlZ RSIRO R]E]Q\\W',
	    'PoUGlG R][f[ R^N^Z R`C`G RhPlX R^JcM RfVfZ RYQWX RUGUSSZ',
	    'PoRKlK RY[][ R]O][ ReRiW R[KTR R`RcX RbKhQlR RTF^EZM RWRUX ReD]E RhD`E',
	    'PoRFlF RZ[e[ R[S[[ R_C_G RgTlY ReXe[ R\\QbS RVTTY RXF]LbOkQ RfFbK\\OSQ',
	    'Po\\IlI RWCW[ RdCdI RYH[L R_JcThYl[ RiKeT`Y[[ RSIRO',
	    'PoUNiN RZ[e[ RUGUP RZTZ[ R_C_Q RUGiGiO RgTlY ReXe[ R\\RaT RVTTY',
	    'Po]QlQ RWCW[ R]I]S RdCd[ R]IlIlS RYH[L RSISO',
	    'PoTEjE RRPlP RZ[e[ RZTZ[ RUIfIfP R\\RaT RgTlY ReXe[ RVTTY R]EZP',
	    'PoWCW[ R_McUgYl[ R]SY[ R^MkMhTcX^[ RYHZL RSIRO R^E^M\\X R[EhEgM',
	    'Po^HkH R[PlP RWCW[ RdHd[ RZGZK R`C\\L RSIRO',
	    'PoWGkG RXJhJ RY[`[ Ri[k[ RYSY[ R`X`[ RlWl[ RVHRL RcUeY RZQ^S RXCUJ RUUTY RTNgNgThZ',
	    'PoWCW[ RhMh[ ReGlM RcG\\M RYHZL RdCfH RdCaI RSIRO R`M`S_X',
	    'Po\\JlJ Re[k[ RWCW[ ReNeZ RkWk[ RfDjH R^VZZ RYHZL R`Q]X RSIRO RbCaK`T',
	    'Po]FjF R[NlN Rf[l[ RWCW[ RfNf[ RlWl[ R_WZ[ RYHZL RSIRO R`N_W',
	    'PoUPhP RZ[e[ RZUZ[ RgUlZ RdLiQ ReXe[ RYFTK R[MYP RUWRZ R\\SaU R\\EcEgIlJ R]I[N',
	    'Po_YjY R[YeY RWCW[ RYH[L RhTk[ R`EfDkN R_F[N RSHRO RbM^Y',
	    'PoZPlP RVCV[ R\\HiHiP RYHZL RSHRO RcCcPaU[[ RcQfWk[',
	    'Po_[d[ RWCW[ RdMd[ R^EjEdJ R\\MlMjPhR RYH[L R_HeJ RSIRO',
	    'Po\\JlJ RWCW[ RcJc[ RYH[L RcNlR R`DgF RSIRO',
	    'Po[HlH RWCW[ RcCcN R^N`SeXl[ R\\NiNgRbWZ[ RYHZL RSHRO',
	    'Po]NlN RWCW[ R]F]Z RYH[L RSIRO ReXZZ RlWk[i[ RfEfNgY RiD]F',
	    'PogNlN RZSlS RWCW[ RcNc[ R\\GgGhN RlKlN R`K\\O RYHZL RSIRO RaC`L',
	    'Pod[k[ RWCW[ R\\I\\M RdCdNeZ R\\IkIkL RlVk[ RaV[[ RYH[L RbQ_X RSIRO RcHbU',
	    'Po\\KlK RWCW[ RgVl[ RaUeY R`V[[ RYG[K RdOiY RbP_X RdCdLgV RSIRO RcIaU',
	    'Po]SjS Rd[l[ RWCW[ R]K]V RdCdZ RjKjU RlXl[ RYHZL RSIRO RjF]H',
	    'PoWCW[ RcJfN ReFlM RdDfH RcF\\M RYHZL R]QjQe[ RdCbH RSHRO',
	    'PoZJdJ RfTlZ R[R`T R_CcGlK RVTTZ R_CZGRK R[U[[e[eY RVNgNbR',
	    'Po]FjF R[NlN RcYkY R^ZfZ R[ZdZ RWCW[ RYH[L RfSjVl[ RSHRO RaN^Z',
	    'Po\\YlY RVCV[ RYHYM R\\H\\Y RlHl[ RcP_U R_FaHcKeNhT RhFbR RSISO RhDeN',
	    'PoZOlO RZZlZ RWCW[ R`S_Z R`P`W RaMaT RaGaP RaEaN R\\EiEiT RYHZL RSIRO RhUgZ',
	    'Po\\EkE R\\OjO RZZlZ RWCW[ RcEcZ RYH[L RSISO',
	    'PoWCW[ RfVk[ RaU\\[ RYH[L RdQhX R_IkHjN R`F[O RcP`W RSHRO R`D]M RcIeT RcKbT',
	    'Po^MlM RWCW[ RfMf[ RYH[L RSIRO R^F^P\\X RjD]F',
	    'Po[HlH Rh[l[ RWCW[ RcCcH R_MhMh[ RlVl[ R^WZ[ RYHZL RSIRO R_M_S^W',
	    'PocRgR RZ[e[ R[U[[ RgUlZ ReXe[ RYERK R]HVO R]SbU RUUTY RXGiGhQ RdGbK[R',
	    'Po^HlH R^KjK RVCV[ RYHYL R_C\\J RSIRO R[OiOiUi[ RlXk[l[',
	    'PoaReR RZ[e[ R[U[[ RgUlZ ReXe[ RYETK R\\SaV R]EeEiIlJ RUUTZ R]K[OYQUS RWKfKeR',
	    'Po[ElE RWCW[ RdHd[ ReKlR RcJ[R RYHZL RdEaM RSHRO',
	    'PoRHlH RY[f[ RYTY[ R^O`Q R^SbW RgXf[ RhTkZ R`HaKeOlR RUTSZ R]KYORR R_C]L',
	    'PoY[f[ RYTY[ RYH^M R]RbW ReKkP RdGgM RbL^P RgXf[ RXKSP RiTkZ RUTRZ RdG`O RZDWM RdDbM',
	    'Po\\EjE RZMlM RdZlZ RWCW[ ReMeZ RlXlZ R_VZ[ RYHZK RaP^X RSIRO RcEbJaT',
	    'Po\\EkE R\\ZlZ RVCV[ R\\E\\Z R`IdN RdR_W RXHYL RfOgRiV RhHdR RSIRO',
	    'Po[NlN RWCW[ R_D_[ RfUlZ RjEcK RYHZL RcNgW RfX^Z RSHRO',
	    'PobTgT R_[j[ RWCW[ R_M_Z RjWjZ ReFlM RdCfH RcCbF[M RYHZK RSIRO R_MhMgT',
	    'Po[ElE R^TjT R[[l[ RWCW[ R^E^Z R^LjLjT RYH[L RSIRO',
	    'Po[KlK RcTiT R^ZlZ RWCW[ R^D^Z RcCcT RiDiT RYHZM RSIRO',
	    'Po^RfR ReUjU R^[k[ RVCV[ R_L_Z R^LfLfR RlWkZ RYGYK R`C\\L RSIRO R^HkHkPjU',
	    'PofNkN R^ZlZ RWCW[ R^Q^[ R^QlQl[ RbJ]N RYH[L RdE`L RSHRO R]ElEkN',
	    'Po]KkK R]QlQ RWCW[ R]E][ R]EkEkK RXHZL RSIRO RcY[[ RlWk[i[ RdLfX',
	    'Po[FkF RZZlZ RWCW[ RcIcX RfKkQ RcI\\Q RdFbK RYHZL RSIRO',
	    'Po_HlH RbMkM RbTlT RVCV[ RbHb[ R`C\\M RYHYL RSISO',
	    'PoXGkG R^KjK R^OkO RZ[e[ R[U[[ R^G^R RgTlZ RfXe[ RWHRM R\\SaU RYCVJ RVUTY',
	    'PoZQlQ RVCV[ RXHYL R]H]Q R]HiHiQ ReUjZ RcCcQgX RbU^YY[ RSHRO RcMbU',
	    'Po^UfU Rd[j[ RWCW[ R^M^V R^MfMfT RZGZJ R`C\\L RSHRN R^HlHlSj[',
	    'Po\\MkM R[SlS RWCW[ RdCd[ RYHZL R^E`K RiEgK RSIRO',
	    'PoZ[e[ R[T[[ RfTlZ ReXe[ RaFcKgOlQ RTL]P R\\RaU RWCSM RVTSZ RRG]G[MWPRR R_EjEhKdO_Q',
	    'PoVCV[ RXHXL RiWl[ R[RaX R_VZ[ RfVa[ RfQjY RiNdY RaP]Y ReKhW RjIgU RSIRO RdHgU R^C[T RZIbI_U RbFkFhS',
	    'Po\\FlF RdOkO RZZlZ RWCW[ R^L^Z RdFdZ RYHZL RSIRO',
	    'Po^QlQ R^ZlZ RWCW[ R^H^[ R^HlHl[ RYH[L ReDcH RSISO',
	    'PoZHlH RgWkW RRIRO RVCV[ R^N^W RdJd[ R^NkNkW R`JYP RXHYL RbC_L',
	    'PocIlI R]YkY RWCW[ R]P][ RcCcP R]PkPk[ RYH[L RSHRO',
	    'PoSGkG RVQhQ RZ[e[ RVKVQ R[U[[ R_C_K RVKhKiQ RgUlZ ReXe[ R\\SaU RUUTY',
	    'Po\\JlJ R]ZjZ RWCW[ R]Q][ RdCdQ R]QjQj[ RYHZL RSISO',
	    'Po^LiL R^SiS RZZlZ RWCW[ R^E^Z R^EiEiZ RYH[L RSIRO',
	    'Po^MjM R^UjU R[ZlZ RWCW[ R^E^U R^EjEjU RYHZL RSISO',
	    'Po^LhL RfYjY RWCW[ RbQb[ R[QjQjX RcClL RbEZM RYGZK RSIRO',
	    'PoUJiJ RUPiP RY[e[ RUEUP RZTZ[ R_E_P RUEiEiP RfWe[ RiUkZ R\\RbU RUTRZ',
	    'Po^QlQ R^ZlZ RWCW[ R^I^[ ReCeY R^IlIl[ RZH[L RTHRO',
	    'Po]MlM R[ZgZ RWCW[ R]F]W RYHZL RSIRO RjD]F ReUZW RlVlZjY ReEeKgV',
	    'PoWRhR RZ[e[ RWLWR RZUZ[ RWLhLhR RgUlZ RUWRZ ReXe[ R[DVI R\\TaV RTIhH ReFjJ',
	    'PocMkM R`MhM R\\NeN R]ZjZ RWCW[ R]R][ R]RjRj[ RgIlM RYH[L RbC]M RSIRO',
	    'Po]IkI R[PlP RWCW[ ReUk[ RYGZK RcCcQgX RcPaV^YZ[ R^E\\L RSIRO RcLbU',
	    'PoWHiH RSLkL RZ[e[ R[U[[ RgUlZ ReXe[ R_D_LcPjS R\\SaV RXDVJ RVUTY R^M[PTS R_G^N',
	    'Po[GlG RbRgR RZ[e[ RWFWR R[U[[ RgCgR RgUlZ R]JaO RfXe[ RWFRK R]SbU RYCWH RVUTZ',
	    'PoVNhN RURhR RZ[d[ RZUZ[ RUJhJhR R\\TaV RgUlZ RVWRZ ReXd[ RYDRK RXFcFaJ',
	    'Po\\ElE RZQlQ RWCW[ RcEc[ RgHjN R_H\\N RYHZL RSHRO',
	    'Po^JlJ R]QkQ R[ZlZ RWCW[ RdCdZ RYHZL RSISO R_E\\M',
	    'PoeKiK RaQkQ RZ[e[ R[T[[ RbEbQ RaEjEjK RVIZM R\\RaT RgTlY RkNkQ ReXe[ RXFRK RXCVI RVTTY RWF^F\\LXPSR',
	    'Po\\KjK R`[k[ RVCV[ RaNa[ R\\EjEkK RlWk[ RYHYL RjP`T RSIRO R\\E\\S[Z',
	    'Po\\SkS RZZlZ RWCW[ RcPcZ R_GcLkO R\\EjEdK\\O RYHZL RSIRO',
	    'Po\\MkM RVCV[ RfCf[ R\\GkGkM R_VZ[ RYHZL RkYhY RSIRO R\\M[SlRkY RaCaP_V',
	    'PofQlQ R]YlY RWCW[ R]E][ RgEgQ R]ElEl[ RYHZL RSIRO RbEbLaQ',
	    'Po`[h[ RVCV[ R`I`[ RcQZZ RYHYL RiMlT R_DeF RiEdP RiVi[ RSIRO R\\MZT',
	    'PoVCV[ RXHYL R]SZV R_D[N RaK^S RjZg[ RSHRN RaRbWZV RdIlIlTk[ RgCgMeX RaM[N',
	    'Po\\HkH R[PlP R`ZkZ R]ZhZ R[ZfZ RWCW[ RjWk[ RgUjX RYH[L RcCcP]Z RSIRO',
	    'Po[HlH RVCV[ RYHYM RbDeG RhLdP R\\QeP\\Y R`L\\P RhTk[ RaH_N RSIRO RcY\\Z RgX`Y RjXcY',
	    'PocRhR RZ[e[ R[U[[ RgUlZ ReXe[ RZCSK R]HVO RbK[R R]SbU RUUTY RdFaM RXJbM RXGiGhQ',
	    'Po[ElE Rg[k[ RVCV[ R\\J\\[ R\\JkJk[ RYHYL RdEdNfPhT RdMaT RSHRO RdHcQ',
	    'Po]OjO Rf[l[ RWCW[ R]E]O RfOf[ R]EjEjO RlWl[ R_W[[ RYH[L RSHRO R`O_X',
	    'Po\\IkI RVCV[ R\\I\\Y RgDjG RgT`[ RjLfV RaV[Y RYHYM RlWk[iZ RSIRO RdCcIeV',
	    'Po\\IkI RhYlY RWCW[ RcCc[ RiLiY RlTlY RfEjF RYHZL RSIRO R^L^Q]W',
	    'PoVCV[ RdCdN RXHZL R_NbTfYl[ R]GlGkL R]NjNhTcX][ RSIRO R]G\\R[Z',
	    'PoRGlG RePlP RY[f[ RYSY[ R_C_Q RfJfP R^SaV RfXf[ RiTkY RcDgF RTTRZ RWORQ RXJXO',
	    'PoRHlH RZ[e[ R[T[[ R_C_Q RgUlZ ReXe[ R_GfNlP R]SaU RcDhF RUUTY R_GXNRQ',
	    'Po^RjR RZ[e[ RWGWR R[U[[ R^E^Q RcCcN RgUlZ RjOjR ReXe[ RWGRL R]SbU RYCVI RUUTY RiFhM RiFZI',
	    'Po_[c[ RVCV[ R]IcIc[ RfSlX R^SYX RkLfQ RYGYK RcKhU RZN`N\\V RcIeR R_DgF RSIRO',
	    'PoWPhP RZ[f[ RWIWP RZTZ[ RWIhIhP R^RbV RhUkX RZD[H RfXf[ RdFaI RUUSZ ReCdG',
	    'Po_GlG ReRiR RY[f[ RYTY[ RiCiR RWL\\Q R]SbW RaKbO RfXf[ RhTkZ RSJWK RUURZ RRF]FZLVPRR',
	    'Po\\HjH R[OkO RWCW[ RcCc[ RfRlY RcMeR R`SZY RYHZL RcM`S RSISO',
	    'Po_QkQ RWCW[ R_E_P R_EkEkP RYH[L RhTk[ RaT^Z RSHRO',
	    'Po\\RiR RZVlV RVCV[ RYHYL RcOc[ R]EbKjN R[EiEeJ\\N RSHRO',
	    'PoaFiF RcQlQ RWCW[ RkReX RcD]J RYHZL R_HbJdL]P RiGdM`S\\U R`ScTfW^[ RSIRO',
	    'PoYJkJ R[QjQ RZ[f[ RWGWR R[U[[ RcEcQ RgTkY RfXf[ RXERK R^TcV RVUTZ RiD\\E',
	    'Po^QfQ R^VfV Rd[j[ RWCW[ R^L^X R^LfLfV RYGZK R`C\\L RSIRO R^HlHlSj[',
	    'Po\\GjG R[MlM R[QlQ Rc[g[ RVCV[ RcCcM RhMh[ R^TbX RXHYK RSIRO',
	    'Po[HlH R[MkM R]ZjZ RWCW[ R]R][ RcDcM R]RjRj[ RYGZL RSIRO',
	    'Po\\GhG R[MlM R`[k[ RWCW[ RaQa[ RbCbM RlXk[ RkEiI`RZU RiQaU RYGZK RSIRO',
	    'Po[ElE R_KiK RYZlZ RVCV[ R`TdU RYHZL RaNfP RSIRO R`E]T RiMfZ',
	    'Po\\ElE R`IjI R`OjO R_VkV R\\ZlZ RVCV[ R\\E\\[ ReIeV RYHZL RSIRO',
	    'Po\\JkJ R]PkP R[UlU RWCW[ RdKd[ RgFlJ RaE_I R_L\\S RYHZL RSIRO',
	    'Po^MkM R^UkU R^ZkZ RWCW[ R^C^L R^P^[ R^PkPk[ RlJkM RYHZL RSIRO RjF]G',
	    'PodMhM RVCV[ R\\WY[ RfWb[ RXHYL RbI_L[M RaZ]Z RhZkZgZ R]FjGiM RSIRO R^O\\X RbCaJ RdRlRkZ RZRbRaZ RgOfX',
	    'PoSGkG R_RbR RZ[e[ R[T[[ R_C_G RbGbQ R\\S`U RgUlZ RfJkO ReXe[ RWJRO R[NWR RVUTY R\\G[O',
	    'Poe[l[ RVCV[ RfDfZ RjHhL R_VZ[ RlWl[ RYGYK R\\H]M RePkS R`QZS RSHRO R`D`R_W',
	    'Po[NlN Rf[l[ RVCV[ RcCcN RfNf[ RlWl[ R_W[[ R\\E_K RiEfK RXHXL RSHRO R`N_W',
	    'Po[ElE R`MjM R\\MhM R[SjS RZZlZ RVCV[ RcNcZ RgIjL RbE`I\\M RYHYM RSIRO',
	    'Po^KlK RaRfR RY[f[ RYUY[ RfHfQ R]SaW R`EjEgG RfXf[ RiUkZ RTL]P RXCTL RUUSZ RRG]G[MXORR',
	    'PoSE\\E RhPlP RZ[e[ RXEXN R[T[[ R_EhEhP RlNlP R]RaU RgTlY RbIeL ReXe[ R_E_L[Q RVTTZ R\\MRO',
	    'Po\\KlK RfUjU Rb[k[ RWCW[ RbNbZ RaNjNjU RlWk[ R^H[L RZHZK R`FgFeJ RSIRO R`C]J]S\\Y',
	    'Po]EkE R^PiP R^UiU R[YlY RWCW[ R^J^U R^JiJiU RYHZL RSIRO',
	    'Po\\ElE RfRkR R\\ZkZ RVCV[ R\\K\\[ RaEaQ RgEgR R\\KkKk[ RYHZL RSIRO',
	    'Po[HlH RWCW[ RcCcG RgJlO R_K[O R_OaSfXl[ RgOeT`XZ[ RYHZL RSIRO',
	    'PoaPkP RZ[e[ R[T[[ RaFaP RaFkFkP RgTlZ ReXe[ RUL^O R\\RaT RWCSL RVTTZ RRG^G[LXORR',
	    'PoURiR RZ[e[ RUFUR RZUZ[ RUFiFiR RgUlZ ReXe[ R^D\\F RYIdN R\\SbV RVUTZ RcH^MXP',
	    'Po[GlG R_MgM R\\PjP Rb[f[ RWCW[ RfIkN R`IZO RYHZL RbC^L R^P^UhTg[ RSIRO',
	    'Po_JlJ RVCV[ RYHYL R^G^[ ReCe[ RfLkV R^FYP RdM_W ReHhR R^C\\L ReHbS RSHRO',
	    'PoTHjH RULiL RRQlQ RZ[e[ RZTZ[ R_H_Q RgUlZ RXD[F ReXe[ RfDcF R\\SaU RVUTY',
	    'PoUFiF RSJkJ RUNiN RRRlR RZ[e[ R[U[[ R_C_J R_K_R RgUlZ ReXe[ RUVTZ R\\SaV',
	    'Po`TgT R\\YlY RWCW[ R\\E\\[ R`J`T R`JgJgT R\\ElEl[ RYHZL RSISO',
	    'Po[IlI R[OdO RVCV[ RXHYL R`O`X RhDkG RfCfMgY RSIRO ReWZX RlWk[i[',
	    'PoeOiO RZ[e[ RYFYP R[T[[ RgTlY RcK^P ReXe[ R\\RbT RVTTY RdEbM R]HTK R\\ERG R]MRO R_EjEjJiO',
	    'Po[GlG R\\NjN RVCV[ R\\JjJjN RXHYL RcCcSeWgYl[ RaU^YZ[ R\\N[RkRjVhV RSIRO RcMbU',
	    'Po]WhW Re[j[ RWCW[ R]M]X RhMhX R`MfS RcP^U R]IZL RYGYJ R_C\\K ReKbR RSIRO R^HlHlTj[',
	    'Po[IlI R`QkQ R`ZlZ RVCV[ R^M^[ RfKfZ R^MYR RYHYL RbD]O RSIRO',
	    'Po[OlO RaZgZ R_ZeZ RWCW[ RYHZL RhUjWl[ R^EkK R_Q[Z RiDeG\\K RSIRO R`L]W RdRaZ RjYeY',
	    'Po\\GlG RVCV[ RkLiR R_M_R RYHYL RSIRO R]G]O[X ReHePbW^[ RePgWk[',
	    'PoSFZF RZ[e[ R[U[[ RgUlZ ReXe[ RcLfPlR R\\SaU R]GjGiJ R^C[J RVUTZ RaL^OYR RZMRO RbGaM',
	    'PoZZlZ RVCV[ R\\H\\Z RaHaZ ReHeZ R\\HjHjZ RYHYL RSIRO RbCaK',
	    'PoTJ[J RTP[P Rc[i[ RTETV RcJc[ RjWi[ RdEgH RjMlU R_M^V R[URW RRE\\E\\[',
	    'PoaUhU Rh[l[ RWCW[ R]H][ RaMaV RaMhMhU R]HlHl[ RYHZL RcCbH RSIRO',
	    'PoSEkE RfQiQ RZ[e[ RUIUQ RZTZ[ R\\I\\P RbIbP RUIiIiP R\\RaT RgTlY ReXe[ RUTTY R_E]I',
	    'Po^IjI R^OjO RWCW[ R^D^Z R^DjDjO RdUjZ RYHZL RbOfW RjReU RSIRO RdY[Z',
	    'PoYIeI RUQiQ RZ[e[ RUEUQ RZUZ[ RUEiEiQ RgUlZ ReXe[ R_LeO R\\SaU RVUTY R_G_LYO',
	    'Po_GiG R^ZiZ RWCW[ R^R^[ R^RiRi[ R`E\\J RYHZL R_IeOlR RhHbNZR RSIRO',
	    'Po`IiI RaUhU Rh[l[ RVCV[ R]E][ RaNaV RaNhNhU R]ElEl[ RYHZL RSISO',
	    'Po\\LlL R]ZiZ RVCV[ R]R][ RdEdR R]RjRj[ RXHZL RSISO RjD]F',
	    'PoUGiG RSMlM RY[][ R]P][ RgThX RdRhU RaSbX RdCdMhQlS RXSVW RZCZMVRRS',
	    'Po^MhM R]SiS RZZlZ RVCV[ RcMcZ RcDjK RbE[M RSISO RXHYL',
	    'PoWJgJ RWNgN RWRgR RZ[e[ RWFWR RZUZ[ ReYd[ RWFgFgR R^TaW RgUlZ R^C]F RUUTY',
	    'Po^MhM R]ZiZ RWCW[ R]R][ R]RjRj[ RdElL RbCaF[M RYGZK RSIRO',
	    'Po_OiO R\\OeO Re[l[ RWCW[ RfRf[ RlXk[ RfLjP RhGlK R^W[[ RYGZJ R`EgDiI R^F\\K RbI_O R`R^Y RSHRO',
	    'Po\\JkJ R[QlQ RVCV[ RhJh[ R_W[[ RYHZL R_C`H RiCgH RSIRO R`J`R_W',
	    'PoUHgH RUKgK RZ[e[ RUDUR RZUZ[ RUDgDgK R^TbX RgUkY ReXe[ RUUR[ RiMcO R^K`NdQkR R_PUR',
	    'PoRGkG RVNhN RVQhQ RZ[e[ RVJVQ R[U[[ RVJhJiQ RgUlZ R^C_G ReXe[ R\\SbV RUUSY',
	    'PoSFkF RUNiN RURiR RZ[e[ RUJUR R[U[[ R_C_R RUJiJiR RgUlZ ReXe[ RUWRZ R\\TaV',
	    'PoSEkE RRPlP RY[f[ RYTY[ R\\E\\P RbEbP R]RaW RfXf[ RVHXM RhTkZ RUTRZ RhHfM',
	    'PoZHlH RZ[e[ RWGWR R[U[[ RbCbS RgUlZ RdJkQ RVWSZ ReXe[ RXERK RaJYQ R\\TaV',
	    'Po[FbF RZMcM RVCV[ RXHXK R]MZW RkZgZ RSHRO R`QbVZV RcJlJlTk[ RgCgMeX',
	    'Po\\EkE R^MkM RWCW[ RgVl[ RgHiK RaV][ RYHZL RePiY RcR`Y RdGdMgW RSHRO R\\E\\RZY RdJbU',
	    'Po\\JkJ R]SkS R][k[ RVCV[ RYHZM R\\D\\I R]S]Z RdCdI R\\NkNkS RkDkI RlWl[ RSIRO',
	    'Poh[l[ RVCV[ RXHYM R\\E\\U R\\EdEdU RhFhU RlDl[ RaWeZ R_WZ[ RSISO R`H`R_W',
	    'Po[HlH R\\YkY RVCV[ R\\K\\Y RkKk[ R_LfT RdQ^W RYHYM RbCdH RgJcS RSIRO',
	    'Po]KjK R]RjR R[VlV RWCW[ R[E[I RdMd[ R[EkEkI RYHZL RaH]Q RSISO',
	    'Po[GlG R]NlN R][k[ RWCW[ R]N]Z R`C`K RdJdN RgCgK RYHZL RSIRO',
	    'PoVMiM RVPiP ReTiT RY[f[ RVIVS RZVZ[ R_I_S RVIiIiS RhVlZ RfXg[ RUVTZ R]UcW RUDgD_H RZFdI',
	    'Po\\PlP R\\UlU Rh[l[ RWCW[ R\\K\\[ RdKd[ R\\KlKl[ RYGZJ R`GiK R\\ElEdI RSIRO',
	    'Po_JkJ R^QlQ R^UlU Rh[l[ RWCW[ R^M^[ R_D_J R_DkDkJ R^MlMl[ RYH[L RTHRO',
	    'Po]HcH ReNlN R\\TjT RZZlZ RWCW[ R]D]O RcPcZ RfDfN RlLlO RYHZL RkFeI RSIRO RcN[N',
	    'Po_KjK R\\YlY RWCW[ R\\E\\[ RdGdV R\\ElEl[ RgPjS RbP^S RYHZL RdJhR RdJaR RSIRO',
	    'Po^QjQ R^UjU Rf[j[ RWCW[ R^L^[ RdCdL R^LjLj[ RhElI R`F\\J RYH[L RSIRO',
	    'Po]QiQ Re[k[ RWCW[ R]I]Q RfQf[ R\\JiJiQ RlXk[ R_W[[ RYGZJ R`EgDjJ R^F\\K R`Q^X RSIRO',
	    'PoYIeI RTLkL RZ[e[ R[U[[ RgUlZ RVWRZ ReXe[ RdNkQ R]TbV R_DjI RXOSQ R^DSJ R_I_R[R',
	    'Po^KiK R[QlQ R_[c[ RVCV[ RdKd[ RjUkY RhTkW R^U[Y RYHYL RdDhHlJ RdC_I[J RSIRO',
	    'Po[IlI RZPdP RVCV[ RXHYL RaLaW RhDkG RgT`[ RjLfV RSIRO R]L]S[Y RfCfNgY RlWk[i[',
	    'PoRKlK RZ[e[ R[T[[ RTFXE]EaE`Q RWGYI RgTlY RgGdJ ReXe[ RaKcNlR R\\SaU RVUTY R]KZNRR RhC_E',
	    'PoRG]G R`JlJ RSRXR RZ[e[ RXCXQ RZUZ[ R`E`M RgJgQ RgUlZ RUWRZ ReXe[ R\\SbU R]P[Q RjD_E R\\KRL',
	    'Po[GlG R]ZiZ RWCW[ R]R][ RcCcG R]RiRi[ RYHZL R^HcMlQ RhHdM[Q RSIRO',
	    'Po]NkN RWCW[ RdHd[ R\\HkHkM R_C`G RiCgG RdQ]Y RYHZL RSIRO RkYgY R]M\\RlRkY',
	    'Po]IjI R]MjM R\\PkP R[UlU RVCV[ R]E]M RcPc[ R]EjEjM RYHZL RSISO',
	    'Po]HkH RZNlN R]ZjZ RWCW[ R]R][ RcCcQ R]RjRj[ RYGZK R_E[L RSIRO',
	    'Po[HkH RVCV[ R^NaQ RhNlR RXHYL RcCcQeWhYl[ RhKeR R^L[S RaU^YZ[ RSHRN RcKaV',
	    'PoZ[e[ RYHYQ R[U[[ RgUlZ RdIlQ ReXe[ RZDUH RVMRO RYJTN R]F_K R]SbU RjFhJ RUUTY RcL`O\\Q RTCXGSI RXRTR RXFYN RdCcM',
	    'Po[GlG R]PkP R]UkU Rg[k[ RWCW[ R]L][ RdCd[ R]LkLk[ RfDjE RYHZL RSIRO',
	    'Po]JiJ R\\SkS R][k[ RVCV[ R]M]Z R]D]J RcMcS R]DiDiJ R\\MkMkS RlXkZ RYHZL RSIRO',
	    'Po]IjI R]NjN R]RjR Re[l[ RWCW[ R]E]R RfRf[ R]EjEjR RlWl[ R_W[[ RYHZL R`S^Y RSHRO',
	    'Po^HkH RYQlQ R\\WlW Rd[h[ RVCV[ RYHYL R_C\\J RSIRO R^KjKjUh[ R^L\\X RdKcRbW',
	    'Po[LlL RgXjX RVCV[ R^P^Y RdNd[ R]QjQjX R_NZS RYGYK R^DjI RaI^P RiCbG[I RSIRO',
	    'Po[GlG R^OiO R[TlT R`[c[ RWCW[ R[K[N RcCcK RcRc[ R[KlKlN RYGZJ RgPcR RSIRO',
	    'Po\\RkR Rd[l[ RVCV[ R]J]R ReRe[ R[KkKkR RlWl[ RYGYK R`C\\L RaU^YZ[ RSIRO R_FfFdKbU',
	    'PoUEjE RZIgI RWLiL RURkR RZ[e[ RUEUS R[U[[ RgUlZ RUWR[ ReXe[ R[GXJ R]TaV R`L]OXQ R`KcOiP R`H_N',
	    'Po[FlF R]NiN R_[c[ RVCV[ R]J]N RcTc[ R]JiJiN RcCdF RYHYL RSIRO R\\RjRcT',
	    'Po[GkG R\\QjQ RVCV[ R\\K\\Q RcCc[ R\\KjKjQ ReSlZ RbS[Y RXHYL RSIRO',
	    'Po[IjI RVCV[ RfIeOkMkO RgFkJ RaP[V R_ScXk[ RaC_I^N[O R_RhRdW`YZ[ RYHYL RSIRO',
	    'Po]KjK R[OkO R\\TjT RZ[l[ RVCV[ R]D]K RcOc[ R]DjDjK RYHZL RSISO',
	    'Po]JjJ R]PjP R\\UkU RZZlZ RVCV[ R]E]P RdEdZ R]EjEkP RYHZL RSIRO',
	    'PoaKjK R[TlT RWCW[ RaDaK R]F]OiOiT RaDjDjK RYHZL RdVgYl[ RbV_YZ[ RSIRO RcObW',
	    'Po\\DkD R[OlO R]ZjZ RVCV[ R]S][ R]IiIiO R]SjSj[ RYHZL RSHRO RbE`O',
	    'Po`GlG RZ[e[ RWFWR R[U[[ R[G[P RgUlZ ReXe[ RXERK R`GbLfPlR R\\SaV RVUTY RaC^J RiGgLcO]R',
	    'PoRI_I ReRjR R[[e[ RZDZR R[U[[ RbDbN RjCjQ RgUlZ ReXe[ RYIRP RYK_N R\\SaU RVUTZ R^CTE',
	    'Po]KiK R]PiP RWCW[ R]G]Z RcCcF R]GiGiP RYHZL RbPeVhYl[ RiTeU RSIRO ReX[Z',
	    'PoVJiJ RTQjQ RZ[e[ RTMTR RVFVJ R[U[[ R_C_T RTMjMjR RgUlZ ReXe[ R\\TaV RVUTZ RVFiFiJ',
	    'PoURiR RZ[e[ RUFUR R[V[[ RUFiFiR RgVlZ RUWR[ ReXe[ R^C\\F R^GYKdO R\\TaV R]IdI`MYP',
	    'PoWDgD RSGlG RWJgJ RWLgL RVShS RZ[e[ RVOVR RZUZ[ RVOhOhR RgVlZ RUWRZ ReXe[ R\\TaV',
	    'Po]QjQ Rf[l[ RWCW[ R]I]P RfQf[ R]IjIjP RlXk[ RYHZL R^D`H RiCgH RSIRO RaQ_W\\[',
	    'PoZKdK Rh[l[ RVCV[ RYHYL R`E`[ RfFfU RlCl[ R_NdS R_K[U RSIRO RcDZE',
	    'Po`RcR RZ[e[ RWGWR R[T[[ RcFcR RgUlZ ReXe[ RYERK R^FZJ R^J]O RhJjO R]SbU R]GkGiJ R^C\\I RVUTZ',
	    'Po]YlY RWCW[ R]N][ RlNl[ ReRiV RfQ`W RYHZL R]E^K R`OdQ RkDhK RgMeS RcCdI RSIRO',
	    'PoSElE RUNiN RRRlR RZ[e[ RUIUN RZUZ[ R\\E\\R RbEbR RUIiIiN RgUlZ RUWRZ ReXe[ R\\TaV',
	    'PoRFlF RTIjI RdTlT RY[f[ RSLSO RYVY[ R_C_I RZOdOdT RSLkLkO R]SaW RfXf[ RiVjZ RUVR[ RZOYSRT',
	    'PoWHgH RWKgK RROlO R\\ShS RUSbS RZ[e[ RWEWO RZVZ[ RWEhEhO RgVk[ RfQjU ReYd[ RUWRZ RdPgQ R]UbW RZQUS',
	    'Po\\UkU R[ZlZ RVCV[ R\\E\\O R`C`P RcQcZ ReJjO RiIdO RcFfM RbEkEgM RXHYM RSHRO',
	    'Po^KjK R]OkO R\\SlS RWCW[ R^E^K R^EjEjK RdSeWhYl[ RcV`Y[[ RYHZL RSISO RdObW',
	    'Pof[l[ RVCV[ R\\I\\[ RcHdK RaElEl[ R`NgV R\\D_G ReR_W RXHYL R_KhLdT RSIRO',
	    'Po^LiL R[QlQ R`[c[ RVCV[ R\\G\\J RdCdG RdQd[ R\\GlGlJ RhTkW R^U[Y RYHZL RSIRO',
	    'Po[HaH RfHlH RfNlN RfTlT RVCV[ RfCf[ R_W[[ RYHZL RaC`TZT RSHRO R[N`N_W',
	    'PoSF\\F RbFkF RTJ\\J RbJjJ RbNlN RZ[e[ R[T[[ RbCbR R\\RaT RgTlY ReXe[ RVUTY R\\C\\NYQTS R[NRO',
	    'PoSFkF RYKhK RYMhM RYPhP RTSlS RZ[e[ RTITS RYHYP RZVZ[ ReXe[ RYHhHhP RgVl[ RUWRZ R\\UaW R_C^I',
	    'Po\\GlG RZTlT RVCV[ RdCdG RdRd[ R^LaO RfLbP R^LZP RXHZL RfMkO R_I\\O RgIeO RSIRO',
	    'Po^EjE R^HiH R^LiL RZPlP RWCW[ R^E^Z RbPeVhYl[ RiTeU RYHZL RSIRO RdYZZ',
	    'PoRH\\H RaHlH RRL\\L RaLlL R\\ZcZ Rg[l[ RRDR[ R]R]Z RRD\\D\\L RaDaL RcWcZ RaDlDl[ ReShV R\\NbQ RXSUX',
	    'Po\\EdE R\\NkN R^SlS RVCV[ R\\HkHkM RgPjQ RYHYL RdRfWiZl[ RcVaY\\[ RSIRO RdOcX R\\E\\Q[X RiDaE',
	    'Po[IkI R[UlU RVCV[ R\\EcEdN RYHYL RcHgLlN RbI_LZN R]QiQcSc[_[ RSIRO RiD`E',
	    'PoWLgL RWTiT RY[f[ RSFSJ RWIWT RYVY[ RWIgIgM RWPiPiT RSFlFlJ R_UbX RiVlY R^CaF RfXf[ RUVRZ',
	    'Po_PiP R_ZkZ RVCV[ R\\G\\J R_J_[ R_JiJiP R_TkTk[ R\\GlGlJ RdCdF RYHZL RSIRO',
	    'Po]GjG R[KlK R[QlQ R\\VkV RWCW[ RdCdK RdQd[ RYGZJ R_L_P RhLgP RSIRO',
	    'PocFlF R\\NiN R\\RiR RZVlV RVCV[ R\\J\\R RcCcJ RcRc[ R\\JiJiR RYHYM RSIRO',
	    'Po[FkF RZMlM R\\PjP RZTlT RVCV[ R\\IjIjP RYHYK RcCbP_V R^Wk[ RSIRO RhUdYZ[',
	    'Po\\SkS RZZlZ RVCV[ R[G[J RcCcG RcScZ R[GlGlJ RXHZM R_N[O RfGfNlMlO RaG_M RSIRP',
	    'Po\\EjE R[KlK R^[b[ RVCV[ RXHXL RbOb[ RfLlR R`LZR RiRkX RcE^O R^R\\X RfSgW RSIRO',
	    'Po\\TjT RUCU[ R\\O\\[ RaOa[ ReOe[ RcDjK R^KgJlK RcD[L R\\OjOj[h[ RSHRN RXHXL',
	    'PoRH^H R_JlJ RZ[e[ RXCXS R[U[[ RgJgR RWH\\M RfUlZ ReXe[ RWKRO R\\SbV RXGVL RVUTY R_E_K^P RjD_E',
	    'PofMjM R^TcT RfTlT R^ZlZ RWCW[ R^O^[ RfOlOl[ RZG[K RbC^K RdN^O RSHRO RaFlFkL',
	    'Po\\IjI R\\MjM R\\SbS Rd[l[ RVCV[ R\\D\\M R\\P\\[ ReOeZ R\\DjDjM RlWl[ RkRdT RYHYM RbY[[ RSIRO',
	    'PoXF^F RRJ`J RVSYS RZ[e[ RYCYR R[U[[ RgUlZ ReXe[ RVVTZ R]L^P RbFdMiQlR R\\TaV RTLRQ R`EkEhLcP_R',
	    'Po[FkF R[IkI RZLlL R\\SiS R\\WiW Re[i[ RUCU[ R\\O\\[ RcCcL R\\OiOi[ RXHXK RSIRO',
	    'Po`IiI R`MjM RaWiW Rg[l[ RWCW[ RaRaX ReGeP RaRiRiW R^ElEl[ RYH[L RSISO R^E^R\\Y',
	    'Po\\FlF R]NjN RZVlV R_[c[ RWCW[ R]I]N RcCdF RcTc[ R]IjIjN RYHZL R]QiQcT RSIRO',
	    'Po\\IjI R\\NjN RZRkR RVCV[ R\\D\\N RcDc[ R\\DjDjN ReSkY RYHYL RbS]XZY RSIRO',
	    'PobGlG R\\RjR RZ[e[ R[U[[ R\\L\\R RbCbK R\\LjLjR RgUlZ ReXe[ RYLSR RUDZF RSGXI R]TbV RVUTZ',
	    'Po[GlG R]QjQ R_[c[ RWCW[ R]K]Q RcCcG RcQc[ R]KjKjQ RhTlY R_UZY RYHZL RSIRO',
	    'PogUjU Rd[l[ RVCV[ RXHYL R[G[J ReLe[ RdLjLjU R[GkGlJ R[Q_R R_UZ[ RlWl[ R^JZR RbO^X RcCcG R]MbM_V RSIRO',
	    'PofOjO RaRlR RY[f[ RSFRJ RZUZ[ RbJbR RaJjJjN RSFlFlJ R^TaW R_C_F RlPkR RgXf[ RXHSN RiUkY RWMZO RTURZ RWK^J]MYQRT',
	    'Po^LhL R\\QjQ R[[l[ RVCV[ RcLcZ RdDlL RdD\\L R^T`X RYHZK RiSgX RSIRO',
	    'PoTFkF RYJfJ RYMfM RRPlP R[[e[ R[U[[ RfUkZ R]RaV RWVUZ RdXe[ RfCeQhTlU RYCYPVTRV',
	    'Po[JbJ RcJkJ RVCV[ RXHYL R^C^[ RfCf[ R_MaQ RhNlV ReMaV RfHjT R^IZU RfIcT RSIRO',
	    'PoZHlH R\\SbS RVCV[ R\\L\\S R\\LbLbS RhDjF RgUa[ RkLfW RbVZX RSHRO RXHYM RfCfLfOgY RlWk[i[',
	    'PoRGlG RUO]O RY[e[ RUJUN RYVY[ RUJ]J]N RhVkY RhJgMaS ReXe[ R]UaW ReDiE RUVS[ R^QTS R`CaKdPfRlTlP',
	    'PoSI_I RbPkP RZ[f[ RZEZQ RZTZ[ RbFbP RbFkFkP RXI]N R^SbW RiUkY RfXf[ RZIRQ RUUSZ R^DTE',
	    'Po]IiI RZMlM RcWfW R_[i[ RVCV[ R_R_[ RfMlS RgDkH RjXi[ R]D\\H R`MZT RYHYK ReIhP RbH^QgRfW RcC_N RSIRO',
	    'PoVCV[ RgLkN RiFgI RjRfU RYHYK RcJhL RcSdVgYl[ R^F\\J R^Q\\V RbJ`M\\N RbV_YZ[ RSIRO RcObW RcCbK',
	    'Po^IjI R^MjM Rf[j[ RVCV[ R^E^M R^EjEjM R`OZU RbT\\Y ReWa[ RYH[L RhQdY RbQ`V RSIRO R^RlQk[',
	    'PoWGiG RWJiJ ReTiT RY[f[ RWDWJ RYVY[ RWDiDiJ R^UbX RiVlY RfYg[ RTWSZ RZJXMSO R^M\\PVR ReMaQ]S RWNkNiS',
	    'PoVCV[ R`T`[gZg[ RcSdW RiUkZ RbJdL RbCaG[M RXHYK RcDiKlL R^NhNfT R\\U[Z RSIRO',
	    'Po_KjK R`PiP R`ViV Rg[l[ RWCW[ R\\D\\[ R`P`V RdMdP R\\DlDl[ RYHZL RaGbJ RSIRO RgGfJ',
	    'PoVCV[ ReUj[ R]UaY R^IbL RfHkM RiIdN R_VZ[ RiUdZ R_J[N RYHYL RdQgX R[H_I RZT_V RdDkDgK RcQkQgX R[DaE^L RZQaQ]X RSIRO',
	    'PoeQiQ RVCV[ R`T`[hZh[ RaSdU RhUlY ReLaP R_C^F[J RaJ\\O RbG`L R]U[Z RgGdN RYHYL RSIRO R^GkGjQ',
	    'Po]JlJ R]VjV R]ZjZ RWCW[ R]E]O R]R][ R]RjRj[ ReLiO RYHZL RcEfM RlMkPiO RSIRO RiD]E RdN[O',
	    'Po]GkG R\\MlM R^UjU R^ZjZ RVCV[ R^P^[ R`C`M RgCgM R^PjPj[ RXHZL RSISO',
	    'PoaVgV Rg[k[ RVCV[ R\\K\\[ RaPaX RdCdK RaPgPgV R\\KkKk[ RhElI R_F[I RYHYL RSIRO',
	    'PoVCV[ RXHYL R[LaL`[ RhTk[ RgIgScZ RgOjY RgOdY RdIkIjO RSIRO ReDcO RgMiW RbD[F[RZY RgLfW',
	    'Po^IlI R^NkN R^SkS R^ZlZ RWCW[ R_H_[ RfGfZ R_HZM RYG[K R`C]K RfDeI RSIRO',
	    'PoSElE RULiL RUOiO ReQlT RRSjS RY[e[ RUIUO RYVY[ R_C_S RUIjIjO RhVlZ ReXf[ R]UbW RTVR[',
	    'PoSElE RUI[I RbNiN RRRlR RZ[e[ RZUZ[ RbIiIiN RgUlZ RUWRZ ReXe[ R\\TaV RUIUN[N\\R RbEbI R[E[I RbNbR',
	    'PoZMeM Rc[i[ RW[\\[ RWRW[ R[F[M R\\X[[ RdRd[ RiXi[ RWOZQ R\\S^V RdOgQ RfGkK ReJeM RiSlX R\\DaF RVGTL RTSRY R`S_Y',
	    'PoTH\\H ReRiR RZ[e[ RXCXR RZUZ[ RgUlZ ReXe[ RaL]O R\\SaU RUUTY R`C^I RcF_N RgFbQ RTDSK R\\KRM R_GkGiR',
	    'PobGlG R[K`K RbQhQ RbZiZ R[[^[ RUCU[ RXHXL RZD`D`K RfCgF RdVcZ RiLdW ReIbQ RSIRO R[KZQ`R_[ RiUkZeZ',
	    'PoUJ\\J RbJiJ RUOiO RZ[d[ RUEUO R[U[[ RbEiEiO ReUjZ R^SaV ReXd[ RWUUY RcRlT R_C^M[QXSRT R\\DTE',
	    'Po\\GlG RdMlM R_YjY RVCV[ R_S_[ RdIdS R_SjSj[ RcCdG RXHYL RSIRO R\\G\\R[Z',
	    'Po]HiH R]LiL R]PiP RZUlU RVCV[ R]D]U R]DiDiU RgWk[ R`W\\[ RYHZL RSIRO',
	    'Po[IkI RZMlM RVCV[ RhFkJ RYHYK ReMiPlR RaC^I RbI`M]PYQ RbQ\\T RfS]W RiVdY][ RSIRO',
	    'Po[EkE RcKkK RYRlR RY[f[ RWJWR RYUY[ R]I]R RcEcR R]TaW RXCSH RXHSM RiVkZ RUVSZ',
	    'PoSElE RWKhK RWNhN RWPhP RSSlS RY[f[ RWHWS RYVY[ RWHhHhS R^UaX RhVkY RfYf[ RTWSZ R^C^H',
	    'PoVQhQ RVThT RZ[e[ RVMVT R[V[[ R_M_T RVMhMhT RgVkZ R_VaX RVVT[ RZESH RYF^IcJlL RYFgFaI]JRL',
	    'Po\\EkE R_JjJ R^PlP R\\ZlZ RVCV[ R\\E\\Z ReGdQkW RcS_X RXHYL RaLaN RhLhN RSIRO ReKcT',
	    'PoZGbG RZObO RUCU[ RXHXL R_K_[ ReLkLi[ R]DZO RSIRO RlDeFeRcY RcTYU',
	    'Po\\NkN R\\RkR R[VlV RVCV[ R\\I\\R RcIc[ RcCcG R\\IkIkR RjChH RYHYL R\\D^G RSIRO',
	    'PoZHlH R\\LiL RVCV[ R\\O\\W R\\OiOiW R^EjEjL RbX[[ RdXkZ RSIRO RXHYL R^D\\L RdDbL RcRbW',
	    'Po[FlF RaIjI R^MlM R^SjS R^WjW Rf[j[ RVCV[ R^P^[ R^PjPj[ R_HZM RYHYK RaC^K ReIeM RSIRO',
	    'Po]YlY RWCW[ R]O][ R]OlOl[ RfUhW R\\H_L RbHfL RhHlL RkDgH RcMbO R^D\\G ReDbG RYHZL R`RgU RhQdU`W RSIRO',
	    'Po]IkI R]PjP R]TjT R[WlW RVCV[ R\\D\\G R]L]S RcGc[ R]LjLjS R\\DkDlG RYHZL RSHRO',
	    'PoSH^H R`IkI R`NkN R`RkR RZ[f[ RYCYR R[U[[ R`E`R R`EkEkR R[L]N RYK\\L R^SaW RfXe[ RWKRP RiTkY RYGVM RUUSZ',
	    'Po\\JkJ R[MlM Rh[k[ RVCV[ R\\R\\[ R\\D\\J RaRa[ RcCcJ RfRf[ RkDkJ R\\RkRk[ RXHYL RbMaT RSHRO',
	    'PoZGlG RbLhL R]OkO RZSlS RVCV[ R]D]O RbCbL RcOc[ RhDhL RYHYL RcShXlZ RcS^XYZ RSIRO',
	    'Po]JjJ R]NjN R[RkR R\\VjV RZ[l[ RVCV[ R]F]N RcRc[ R]FjFjN RbCaF RXHZL RSIRO',
	    'PoTFjF RVHhH RSKkK RXPfP RXSfS RZ[e[ RYMYS R[V[[ RWNfMfS RgVlZ RVWR[ ReXd[ RbIfNlP R\\UaW R^C\\JWNRP',
	    'Po]KeK R]NeN R[VlV R`[c[ RVCV[ R]H]N RdTd[ R]HeHeN RYHYL R_C\\H R]RgQjQdT RSIRO R^FkFjO',
	    'PoSFlF RRJlJ RYShS RZ[e[ RYMYS RZVZ[ RZCZH RdCdH RXNhNhS RgVlZ RUWRZ ReXf[ R]UcW R]HWORQ',
	    'Po\\IjI R\\MjM R\\RkR R]VjV RZ[l[ RVCV[ R\\D\\M RcNcZ R\\DjDjM RYHYL R]O[T RSIRO',
	    'Po\\JcJ R\\OcO R\\TcT Rh[l[ RVCV[ RYHYM R\\D\\T R\\DcDcT RgFgT RlCl[ RbVdZ R]V[[ RSIRO',
	    'Po]MlM R_UlU Rj[l[ RWCW[ R]E^R_[ RcPc[ RhPh[ R]HlHlL R_PlPl[ RYHZL RSIRO R]M\\Y RdD]E RkC`E',
	    'Po]HjH R]KlK R]VjV R]ZjZ RWCW[ R]D]P R]R][ R]RjRj[ R]DjDjH RdHfNhOkOlP RYHZL RSIRO RbO[P RdO^P',
	    'Po]KiK R_RiR RWCW[ R]D]K RjDjK RbO[U RYHZL RfMlO R_ScXj[ R`EeG RfDcH_I R`M[P RSIRO RhSdX`Z[[',
	    'Po]HiH R]LiL RZOlO RcTjT Rb[l[ RVCV[ R]D]L RcOcZ R]DiDiL R]T_X RYHYL R]TZ[ RSIRO R`ZgZ',
	    'PoZLbL RVCV[ RXHXK R_E_[ R^LaS RfSb[ R_LYW RgCgPjZ RkIjP RfPdY RgKiW RSIRO R^I]Q RdIcO RbDZE RgKeW',
	    'PoRI]I RZ[e[ RYDYS RZVZ[ RYI\\N RgUlZ RUWRZ ReXe[ R\\TaV RkGiL RaG_L R\\CSE RYIVMRQ ReCeJcN_S ReJgNkS',
	    'PoRE`E RUK]K RRO]O RfSiS RUSYS RZ[e[ RYCYH RYMYS RZUZ[ RSHSJ_H_K RaHkHkQ RgUlZ ReXd[ R\\TaV RUVSZ R[LYM RbR_R ReCeHdO',
	    'Po^JiJ R^RiR R^WiW RZ[l[ RVCV[ R[G[J R^N^W RcCcG R^NiNiW R[GlGlJ RYHZL RSIRO',
	    'Po]IjI R]NjN R[QlQ RVCV[ R]D]M R^Q^[ RcDcM R]DjDjM RcQeVhYlZ RkTfV RYHYM RSIRO RdY]Z',
	    'Po[GlG R_JgJ R]SiS R[WlW RWCW[ R]N]S R_D_J RcJc[ RgDgJ R]NiNiS RYH[L RSIRO',
	    'PocElE RcJlJ RdThT RY[f[ RWJWS RYUY[ RhJhS R_TbX RiVkZ R`N[R RfXf[ RWCTG RXHSN R]EaF RUVS[ R[J`K',
	    'Po[JlJ R]PjP R]SkS RZXlX RWCW[ R]L]P RePe[ R]LjLjP RYHZL R]FiFiJ RbCaJ R]S]X RSIRO',
	    'PoZ[e[ RUKUT R[V[[ RUK]K]T R`PdT RgVlZ RePiT RVWR[ RcK`O RhLdO ReXe[ RWN[N RWQ[Q RZT]TaW RXE`EjJ R^ESJ RZIdHlJ',
	    'Po^KgK R[RaR R[VaV R_[a[ Rg[j[ RVCV[ RXGXK R[N[[ R[NaNa[ ReOeW RjNj[ RcCjK RcC[K RSIRO',
	    'Po\\ElE R^MjM R\\UlU R\\ZlZ RWCW[ R\\P\\[ R^H^M RdPdY R^HjHjM R\\PlPl[ RYHZL RSIRO',
	    'Po[E`E R[KkK RZOlO RVCV[ RXHYL R]VY[ R\\G]J RbFcI R_ScXk[ R_Q\\Y RiFgK R_SiSeX^[ RSHRO R`K^V RiC^E',
	    'PodOjO RZTlT RVCV[ R]I]R ReJeO R[JjJjR R_FZK RYGYJ RbSdVgYl[ R_FeFdJ RbRaW^YY[ RaJ_O RSIRO',
	    'PoTH^H RTL_L RZ[e[ RTDTR RZUZ[ RTD^D^H RgUlZ ReXe[ RbGeMhQlR R\\TaV RUUTZ RcC`K RbGkGgNdQ`R RYH[P RYQRR R^O_R]Q',
	    'Po^GkG R^MiM R^PiP RVCV[ RXHYL R^J^P R^JjJjP RbP[W R_C\\I R_TbWfYl[ R^TiTfWbYZ[ RSHRO',
	    'PoTEjE RRJlJ RVPhP RVThT RZ[d[ RVMVS RZVZ[ R_C_E RVMhMhS RhVlZ RVWR[ RdXd[ RdFcI RYGZI R]UaW',
	    'Po[ElE RaOfO RaTfT R\\ZlZ RVCV[ R\\J\\[ RaJaZ RfJfZ R\\JlJl[ RYHZL RSIRO RcEbJ',
	    'Po]MlM R]PlP Re[k[ RVCV[ R]J]R RdJdR R]JlJlR RYHZL R\\EkEdH RcSbW`Y[[ R`GgI RSIRO R[UlUk[',
	    'Po]IjI R]MjM R]YhY Rf[j[ RWCW[ R]Q]Z R]D]M R]DjDjM RbTfV R`NZT RcS_W RYHZL RSIRO R^QlQk[',
	    'Po^HjH R^LjL R[OlO RWCW[ R^D^L R^DjDjL R_QZU RaU\\Z RYHZL RfRcZ RbR`V Ri[f[ RSIRO R^SkRj[',
	    'Po\\GkG RZMlM R]UjU R]ZjZ RWCW[ R]P][ RcCcG R]PjPj[ RYGZK R_H`L RhHgL RSIRO',
	    'Po\\JbJ ReJkJ R]MjM RZQlQ Rc[g[ RWCW[ R\\D\\J R\\DbDbJ ReDeJ ReDkDkJ RYHZL R^UiUh[ R_Q^V RSIRO',
	    'Po[GlG R_JhJ R_MhM RZQlQ R\\ZlZ RVCV[ R\\Q\\[ R_C_Q RhChQ RfQfVkUkV RXHZL RSIRO RaQ`V',
	    'PoTE^E RVSYS RZ[e[ RYJYS RZUZ[ R\\T`U RgUkZ RUWRZ ReXe[ R]FXG RXKRQ RRJ^J\\M RVG\\J RbHdMhQlR RcC_L RbHjHgMdP_R',
	    'Po]ZiZ RVCV[ RYHZL R[F[I R]T][ R]TiTi[ R[FkFkI RaI[N R_KcOlS R_KiKdO[S RSHRO',
	    'PoXSfS RZ[e[ RSFSH RXNXS R[V[[ R_C_F RXOgOgS RSFkFkH RgVlZ RUXR[ ReXe[ R]UaW R[HTK R]FZI_LhO R^LlO RZIeI_L[MRO',
	    'PoVHhH RVKhK RY[e[ RTNTT RVDVK RYVY[ R_D_Q RVDhDhK RhVlY ReXe[ RUVSZ RWReQgQ R]TcW RTNjNjTgT',
	    'Po[PdP RXTfT RSJSM RSJkJkM RWFYH RfLjP RhFdJ RWNTP RZL[P R]KaM R[QYTRW ReU_XT[ RYU_XcYk[ RhCaD RhC^EUE R_F^I',
	    'Po[EkE R]JkJ R[ZlZ RVCV[ RXHYL R[E[[ R`NbQ ReGeQjW RhNlR RcR_W RdOgT RhLfQ R`L^R RSIRO RdKcS',
	    'Po\\DkD R`JjJ R`NjN R^QlQ R\\ZlZ RVCV[ R\\D\\Z R`G`M R`GjGjM RXHYL RcOaTjW RiReV^X RSIRO',
	    'Po[EkE RZTlT RVCV[ R\\I\\Q RaIaQ RcEbI RfIfQ RYHYL RcSdWgYl[ RSIRO RcRaX R\\IkIkQhQ R`YY[',
	    'PoUFlF RWJ`J RXR_R RZ[e[ RXMXR R[U[[ RXM_M_R RgVlZ RhKbS ReXe[ R\\TaV ReDiD RVVTZ RUFUMTR RaCcKePiTkTlQ',
	    'Po\\IiI R\\NiN RY[l[ RWCW[ R[R[Z R\\E\\N R`R`Z ReReZ R\\EiEiN R[RjRjZ RYHZL RSHRO',
	    'PofQiQ R`[h[ RVCV[ R`T`[ RhXh[ RbSdU RhUlX ReLaQ R_C^F[I RaJ\\O RYHYL R\\UZZ RgGdN RbG_KgM RSIRO R^GkGjQ',
	    'Po\\JjJ R\\OjO R`[g[ RVCV[ R\\E\\O R`S`[ RcEcO RhXg[ R\\EjEjO RaQeR RYHYL RiTlY R]TZZ RSIRO',
	    'Po\\KkK R[OlO Rc[h[ RVCV[ R\\E\\J RaEaJ RfEfJ R\\EkEkJ RcLdO R_V[[ RXHYL RSHRO RaSiSh[ RaO`W',
	    'Po[FjF R[KlK RVCV[ RXHYM R\\N\\V R^H^M RcCcH RgHgM R\\NiNiV ReWl[ R`YY[ RSHRO RbRbW',
	    'Po[FkF RZSlS RVCV[ R]L]R R_C_I RfCfI R]LiLiR RcIcSeVgYl[ RbU^YZ[ RSIRO RXHYL RcNbV',
	    'Po\\IjI RZMlM RVCV[ RXHYK R\\E\\I R]O]W RcCcM R]OjOjW R\\EjEjI ReXk[ RSHRO RaYZ[ RcRcX',
	    'Po\\KjK R\\PjP RVCV[ R\\F\\P R\\FjFjP RcFdPd[lZl[ R^WZ[ RbCaF RYHZL RgRfW R`P]X RSIRO RcMdT RjTkWeW',
	    'PoRF_F RSI^I RTQ]Q RZ[e[ RSMSO RYCYI RZVZ[ RSM^M^O RbEhEhIlHlI RgVlZ ReXe[ RbMeQkT R\\TaV RUVTZ RaLjLfQ`S RbEaJ',
	    'Po]EkE R\\IlI R]OjO R]SjS R]VjV RVCV[ R]L]V RdEdI R]LjLjV RYHZL RgXl[ RaX[[ RSIRO',
	    'Po]IjI R]OkO R]SkS R]WkW RWCW[ R]L]V R]D]I R]DjDjI R]LkLkV RgXl[ R`XZ[ RYHZL RSIRO',
	    'Po\\EkE R]IjI RZLlL R\\TkT RVCV[ RcTc[ RhWkZ RhRlU RcCcL^P RfN`T R^W[Z RYGYK R\\ObR RSIRO',
	    'PoRG_G RbIjI RbNjN RTN^N RgSjS RZ[e[ RTJTP RZVZ[ R^J^O RbDjDjS RUCUF RgVlZ RUXR[ ReXe[ R]D[F RYGXPTT R\\TaV R`R^R RbDbLaP',
	    'Po\\FfF R[IlI R]ViV R]ZiZ RVCV[ RYHYM R]S][ RaDaI R]SiSi[ RaL`PjOjP RjDeI[N RSHRO RiL`M',
	    'Po[FaF R]OkO RZSlS R\\ZjZ RVCV[ RYHYL R\\U\\Z RcNcZ RcGcJ RjUj[ R^M[P R]G^K RjGhK RSHRO RjDaE',
	    'Po]GjG R]JjJ R]MjM R]PkP RVCV[ R^F^Q RdFdP RYGYJ RdCdE R^C[K R^SbWfYl[ R\\SiSfWaYZ[ RSHRO',
	    'Po\\HjH R\\LjL RZSlS Re[l[ RVCV[ R\\D\\L RcLcS RfSf[ R\\DjDjL RlWl[ RiMhQ RYHZL R]M_Q R^YY[ R`S_Y RSIRO',
	    'Po]GlG RaLkL R_RlR RVCV[ RdCdG RgWk[ RdV_[ RZK[O RYGYJ ReLeShY ReRbY R]RYS RbI`O RSIRO R]G]R\\Y ReOdW',
	    'PocHjH Re[i[ RVCV[ RYHYL R[L[O R^OjOj[ R^DjDjL R^D\\LlLlO RaRgS RSHRN RcHcL RiU`V R^O^V][',
	    'PoTH]H RaHjH RTM]M RaMjM RTQ]Q RaQjQ RZ[e[ RTDTQ R[T[[ RTD]D]Q RaDaQ RaDjDjQ RgTlY ReXe[ R]SbU RUUTY',
	    'Po_IfI R]OiO R]RiR R^ZiZ RVCV[ R]L^U^[ R]LiLiR R^UjUj[ RcCiJlK RcC[K RYFYJ RSHRN R]O\\X',
	    'Po\\JbJ ReJlJ Rg[k[ RVCV[ RYHYM R\\DbDbJ ReDlDlJ R_Q\\T R_U[X RhReS RiUeY RSIRO Ra[][ ReOlOlWk[ ReJdP R\\J[ObObVa[',
	    'PofHjH R]TjT R]WjW Re[j[ RVCV[ R]P][ R]PjPj[ R\\EjEjH RdCcF R_I]NZO R[JbKbN_N RhIfNdN RYGYK RSHRN RbG\\H RdJlJkNhN',
	    'Po]IkI R\\LlL R]TjT RZ[l[ RVCV[ R]E]I R]O]T RdCdI R]OjOjT RkEkI R`V`Y RXHZL RgVgY RSIRO',
	    'PoRG`G RfSjS RY[f[ RUJWL RZVZ[ RYCYS R^TbX R]J_M RjVkZ ReNaS RgXg[ RYMTR RYN_P RUHSM RUUSZ R]H[M RaHlHlMjS RfCfIeO',
	    'Po^ZiZ RVCV[ R[G[J R^S^[ R^SiSi[ R[GkGkJ RfIjM R^RZS RcM]S R`I\\M RYHYL RcMhRlS RcCcG RSIRO',
	    'Po[FlF R]NjN R]RjR R[VlV RVCV[ R]J]R R`C`H RdRd[ RgCgH R]JjJjR RYHZL RSIRO',
	    'PoaNkN R]NgN RSTWT R`TdT RY[f[ RWHWS RYVY[ ReNeT R^UaX RhVkY RhPkS RhKlO RhHdI R_P\\S RfYf[ RSE\\DWI RTWSZ R^EeE^JeJ\\N RjDcD R[KRM',
	    'Po]HkH R_MjM R_PjP R_SjS R\\VlV RVCV[ R\\L\\V R]D]H R_J_S R_JjJjS RfXl[ R`YYZ RYGYL RSIRO RhD]F',
	    'Po[FlF R\\MjM R\\PjP RZTlT Rc[g[ RVCV[ R\\J\\Q RcCcQ RgQg[ R\\JjJjQ R^VaY RYHZL RfCjE RSIRO',
	    'Po^GkG R^JiJ RZTfT RVCV[ RaOa[ RlXl[ R\\O]R RaVeW RaTZZ ReOcR RYGYJ R_C[K R\\MiMiUiZ RSHRO',
	    'PoTEjE RXKiK RXMiM RZ[e[ RXHXM R[V[[ RXHiHiM RgVlZ RVWRZ ReXe[ RZQVR RePjR R]UaW R`E_H R`M`S]S RTETLSR',
	    'Po[NcN RcQjQ R[RcR RYUlU RVCV[ RYHYK RgNkR RhFgI R\\G]I R[EbFbI RbTfYk[ R_K[M RfKcMZQ RbSaW_YZ[ RSHRO RiC`E',
	    'PoXGfG RUSiS RZ[e[ RUDUS R[V[[ RUDiDiS RhWl[ RdNfP RVWR[ ReYd[ R`LdN R]UaW R`GXK R_KXN RdJaK`MWQ R]IaM`Q]Q',
	    'Po\\LkL R\\PkP RhTkT RZ[e[ R\\I\\S RcIcS R\\IkIkS R_UbX RjVlZ RfXe[ RWOSS RRHWI R_FfH RTVSZ R\\DjDcG RTDYE RZWZ[',
	    'Po`LiL R]LfL R^[a[ Rh[k[ RVCV[ RZPaPa[ RdPkPk[ R\\R^T RfRhT RfIjM RfDkI RaC[I RbG\\M RXHYK RaUZX RkUdX RSHRO',
	    'Po[ElE R\\OkO RZSlS RVCV[ R\\I\\O RaEaO RcOc[ RfEfO R\\IkIkO RXHZL RcRiXlY RcR]XZZ RSIRO',
	    'Po]UjU RZZkZ RVCV[ R]P]U RcMcZ R]PjPjU RhWl[ RbFdH R]G\\K RYHYL R^FbJfLlM R[EjEeJ`L[M RSIRO',
	    'Po]TbT ReTkT R]ZkZ RWCW[ R]O][ RcGcK ReOkOk[ RYFZJ RiFkL R^E[M RcN]O RSIRO RiC^E',
	    'PoTJ\\J R`LjL RTL\\L RXT[T RZ[e[ RZVZ[ RTG\\G\\L ReYd[ RaEhEhIlHlI RgVl[ RVWR[ RaNeRkT R\\UaW RiMeQ_T RaE`J RTDTP\\O\\T RTKST R\\DTE',
	    'PoRGlG RUR]R RSR[R RZ[e[ RZUZ[ R[P^R RTKXO RgUlZ RXDZF R`KdO R`ReRiQkS RUWR[ RdIbL RhLaR ReXe[ RXGWK RZMUR ReCcG R\\TaV',
	    'Po\\FlF R\\OkO R]VjV R]ZjZ RVCV[ R]R][ RdRdZ R]RjRj[ RgLlP RdK`O RhHcM RdE_J RXHZL R]IbK RSIRO',
	    'Po[IkI RZOlO R\\SjS RVCV[ RXHYL RaEa[ RfEf[ R\\LjLjS ReSkZ R`SZZ RcD\\F RjDcF RSIRO',
	    'PoRH^H RaKkK RTM]M RTP]P RaTkT RZ[e[ RTJTT RZVZ[ RTJ]J]T RbDbJ RbMbT RkHkK RkQkT RZT]UaW RgVl[ R[E_H RVWR[ RfYe[ RWCUH RiEaG RiNaP',
	    'PoZFlF RZKlK R]PkP Rh[l[ RVCV[ R^K^P R_C_H RcHcK RcRcZ RgCgH RiRi[ RlXl[ RYHYL R\\ZY[ RSIRO R]R]Y',
	    'Po\\NiN RYZlZ RVCV[ R[R[Z R\\D\\N R`R`Z ReReZ R\\DiDiN R[RjRjZ RbIfK RYHYK RcFaK RSHRO',
	    'Po[FlF R]MiM R]PiP R]SiS RZVlV RVCV[ R]I]S RcCcI R]IiIiS RYHYL RfXl[ R`XY[ RSIRO',
	    'Po]LiL R]PiP RZTlT RVCV[ RXHYL RZEZH R]I]P RcRdT R]IiIiP RZElElH RhXk[ ReWiX R_W]YY[ RSHRO',
	    'PoUDjD RRGlG RVLhL RVOhO RVRhR RY[f[ RVJVR RYVY[ R_D_G RVJhJhR RiWjZ R]UaX RfXf[ RTWSZ R[TSU RcSkU',
	    'PoZEkE R]GhG R]JhJ R]MlM RZNiN RVCV[ R]E]M RhEhO RfUk[ R\\U`Y R[T^W R_UY[ RiUc[ RYHYK RdQgX RcQjQgX RZQaQ^W RSIRO',
	    'Po[FlF R\\MjM R\\QjQ R]WjW R]ZjZ RVCV[ R\\I\\P R]S][ RaCaP RfCfP R]SjSj[ R\\IjIkP RYHYL RSIRO',
	    'Po[ElE R\\MkM R]QkQ R[TlT R`[c[ RVCV[ R\\H\\M RaEaM RdTd[ RfEfM R\\HkHkM RhVlZ R_V[Z RXHZL RSIRO',
	    'PoeFlF R[FbF R]MjM R]QjQ RZUlU RVCV[ R]J]Q R_C_H RhChH R]JjJjQ RdWkZ RbV_YZ[ RYHYL RSHRO RcQaX',
	    'PoRElE RWLhL RWOhO RRRlR RZ[][ RWIWO RZCZG R]S][ RdCdG RWIhIhO RcUhZ RYVUZ R`VbZ RdRfTlW R]OXTRW',
	    'Po^JjJ R_QcQ R\\ZlZ RWCW[ R\\E\\[ R_M_Q R_McMcQ R\\ElEl[ RhGjH RfGgRcW RYHZL RjMfU RSIRO RdT^T RfLgW RjUjXhW',
	    'PoRF_F RTM^M RZ[e[ RTITM RYCYR RZTZ[ RTI^I^M R^SaW RiWkZ RgUjX ReXe[ RbGeMhPlR RWM^P RUUSZ RdCaJ RYLUPRQ RbGkGhLdP_R',
	    'Po[HiH RfNkN RZN`N RVCV[ RXHYL R_K_N RiKlM RfEjH R`C^G RcJgOlQ RgIfN R\\I[M RbK]PYR RdP]S RfS]W RhVcY[[ RSIRO',
	    'PoRF^F RbKlK RTK^K RTN^N RRQ_Q RZ[e[ RTHTN RYCYT RZVZ[ RTH^H]N RhKhT RgVlZ RVXR[ ReXe[ R\\UaW RbEbLaQ RkDbE',
	    'PoZFcF R[NbN R[SbS RYVcV RVCV[ RXHXL R[J[S R^C^[ R[JbJbS ReLkLj[ RSIRO RlDeEeRcZ',
	    'Po\\HkH R\\NlN R]RkR R]UkU R]YlY RVCV[ R\\D\\H R]M][ RdCdH ReNeY RkDkH RYHYK RdJeL R^J[P RSHRO',
	    'PoTF`F RRL`L RTQ_Q RZ[e[ RZUZ[ RYIZK RXNZO RgUkZ ReXe[ RVDRH RbGeMhQlR R\\TaV RUUS[ RdC`J RbGkGgNdQ`R RUHTP RUI^I]R',
	    'PoUDjD RWHiH RWKjK R[QhQ RUTkT RZ[e[ RUDUT R[V[[ R\\M\\Q R]F]I RdFdI ReYd[ RZNhNhQ RhWl[ RVWR[ R]UaW R_J[NWO',
	    'Po\\FkF R[JlJ R]PjP R]SjS R[WlW RVCV[ R]M]S R_FaJ RdCcF RdSd[ R]MjMjS RXHZL RhFfJ RSIRO',
	    'Po[OcO R[RcR RZUdU Rg[j[ RVCV[ RXHYL R[K[R R_E_Y R[KcKdR RSIRO RcDZE RZHdIlJlUk[ RhCgNfUdYYY',
	    'PoZFbF RYKcK RYPcP R^[a[ RVCV[ RXGXJ R^C^K R^L^P RhWl[ RgWc[ RfQjZ RiPeY RfGeLgV ReIkIhV RSHRO RfCdN R\\P[Y R\\TbTa[',
	    'Po[GkG RVCV[ RXHYL R\\P`T RgPkT RgJjM R`P[U RjIeM RjPfT R[J_K[N RcCcReVhYl[ RbU^YY[ RSHRO RcMbU',
	    'Po]GiG R]JiJ R\\QkQ RVCV[ R\\M\\Q R]D]J RaMaQ RfMfQ R]DiDiJ R\\MkMkQ RYHYL R_TaWfYl[ R\\TjTfWbYZ[ RSIRO',
	    'PoZGlG R]KiK R\\PjP R\\TjT R\\WjW RVCV[ RXHYL R\\M\\W R]D]J RcDcJ R]EiEiJ R\\MjMjW RfXl[ R`XZ[ RSIRO',
	    'PoRE^E RSH]H R`LjL RZ[e[ RXCXH R[W[[ RUM[M[R RSKSL^K^M RbDiDiIkHlI RhWl[ RVWR[ ReYd[ RUSSU RbMfRlT R]VaW R^QYR RjMeR_T RbEaJ RUMUT',
	    'PoaHkH R_LlL RaWjW R`[l[ RVCV[ RaPaX RfCfO RaPjPjX RZM^N R[E^G R]R]XY[ RYGYJ RbD`J RYR]R RSIRO',
	    'PoRG_G RUSYS RY[f[ RYUY[ RYCYR RTIVK R]SaW R\\D^F RfXf[ R^I[M RhTkZ RcHdLhPlR RYL]N RUUSZ RdCaK RYLSO RcGkGgMdP_R',
	    'PoSE]E R`EkE RTH]H R`HjH RWJ^J R_JkJ RSK[K RVQhQ RUThT RZ[e[ RXCXL RZVZ[ ReCeL RUMhMhT RhVlZ RVWRZ ReXe[ R\\UaW',
	    'PocElE R[JaJ RbOlO R[OaO Rh[l[ RVCV[ RXHYL R[E[W R[EaEaO RdEdO RhMh[ RlXl[ RfU`[ RiEhOdW R`RbVYX RSIRO RhJgT',
	    'Po_FlF R`NjN R`QjQ R^UlU R`[l[ RVCV[ RYHYK R`J`Q ReCeY R`JkJkQ RYM\\M\\XZZ RZD\\I R\\Yd[ RSIRO',
	    'Po\\EkE RaMhM R^VcV RfVkV R\\ZlZ RVCV[ R\\E\\Z R^P^V RaHaM R^PcPcV RfPfV RaHhHhM RfPkPkV RYHYM RSIRO',
	    'PocMjM RbRlR RZ[e[ RWKWS RZUZ[ RdKdR R]NaR RgUlZ R_EbH RhFkI ReXe[ RXCSH RXHRN R^F[J R\\TaV R_C]I RVVTZ R^K[R RgCeH',
	    'Po[FlF R_JgJ R\\PjP R[SkS R\\WjW RZ[l[ RVCV[ R_C_J RcJcZ RgCgJ R\\LjLjP RYHZL R\\L]Q RSIRO',
	    'PoSG_G R`IlI RRL`L RY[f[ RYVY[ RYCYS RYL]P RTHVJ R^TaX RiVjZ RhOlS RhDkG RdO_S RfXf[ RYLRS ReCeKiQ RUVSZ R]H\\L ReJbQ ReGdO',
	    'Po_EiE RZKeK RXKbK R_MkM RXRhR RXUhU R\\[e[ RXOXT R_C_L R`O`T RXOhOhT RhWlZ RXXU[ R[W[[ R^VbW RTHkHjJ RTHTSS[',
	    'Po^GlG R^KjK R^NjN R[QlQ Rf[j[ RVCV[ R^I^N R^IjIjN R`Q[W RcT\\Z RfWb[ RYHZL RfTdY R_C]H RSIRO R]UkTj[',
	    'PoTH_H RaHlH RWJ^J RUM`M ReShS RZ[e[ R[V[[ RTD_D_H ReXe[ RiCiS R]O`Q RgUlZ RcKdO RVWRZ RWOUR R\\TaV RZMZSXS RTDTLSS',
	    'Po[FlF R\\KjK R\\NjN RZUlU Rb[f[ RVCV[ R\\H\\N RcCcQ RfSf[ R\\HjHkN R^W`Y R[RiQkR RYHYL RSIRO',
	    'PoZGlG R_KgK RVCV[ RXHYL R[N[[ R_C_K RcKc[ RgCgK R[NkNk[ RgSeX R_S]X R]Q_Q`W RgQhW Rh[k[g[ RSIRO R_Q]Q RgQeQ',
	    'Po[EbE R[LbL R[PcP R\\UjU RZ[l[ RVCV[ RXHYL R[E[Q R[HbHbL RcRcZ RhMlR RiKcQ ReHiP RdEkFgN R_L_P R_E_H ReEgN RSIRO',
	    'Po]UkU R]ZkZ RVCV[ RYHYL R]Q][ R]QkQk[ ReDlDlN RiDeI RkHfM R[DbDcNcQ R`D[I RaH\\N RSIRO',
	    'Po\\FlF R]LlL R_NjN R^TkT R^WkW Rh[k[ RVCV[ R^Q^[ RcCdFd[ R_IjIjN R^QkQk[ RYHZL RSIRO R\\F\\RZZ',
	    'PoUElE RULjL R\\SeS R\\E\\L R\\P\\S R`C`F RcEcL RUHjHjL RfPiS RUNlOjQ RYPVS R_ObP R]TUX RZV_XgZ R[VgVbXY[ R^Xk[ RcXV[ RUETRS[',
	    'Po\\FlF R]NlN Ra[d[ RVCV[ RXHYL RdCd[ R_JjJjQ RfUkZ R^SaU R_QjRgW RdPgW RbW]Z RSIRO R\\F\\RZZ',
	    'PoSFkF R\\IbI RfIkI RdThT RZ[e[ RSKSN RWOWT RZWZ[ R\\C\\I R_K_T RbCbI RfCfI RWOhOhS RSKkKkN RhWl[ RVXR[ ReYd[ R\\UaW RWHSJ RXCXH',
	    'PoTF]F RRI_I RSM^M RRP_P RZ[e[ RXCXI RXMXS R[U[[ RlOlS RVJWM RgUkZ RaKeM RcN^R ReXe[ R[J[M R\\TbV RUUTZ RaGiGiNjS RdDcO',
	    'Po[HlH R]KjK R]QjQ R[TlT RWCW[ R]N]Q R]E]K RdCdQ R]EjEjK R]NjNjQ RYGZK RTHSO RaQ_U^Xk[ RhUeY[[',
	    'Po]HiH RZLlL R]ShS R]VhV R]ZhZ RVCV[ RXGYK R^N^[ R]PiOh[ RfLlR RgDkH R]D\\H R^NYS ReHhP RaG]P RbC_N RSIRO',
	    'PoUFlF R[I`I RWMaM RZ[e[ R[V[[ R^O`Q RgVlZ ReOiS RVWS[ RgM`S ReXe[ RXOVR RcKfR R\\TaV ReDiD RhIeP R\\G\\RYR RlPkTiS RbEdP RUFULTS',
	    'Po[GlG R`LdL R\\PdP R^Z`Z RUCU[ RXHXL R`I`Z RhDjF RgVcZ RcSdV RjOeY R]S\\X RjLgV RSHRO RlWk[i[ RfCfMfOhY R[G[RY[',
	    'PoUR]R RZ[e[ RUMUR R[U[[ RUM]M]R RgUlZ RZG^L R[D^F RgNkS RdN`R ReXe[ RWDSG RXGSM ReJhQ R\\TaV RVVTZ RcC`J ReJcP RbGkGjJ ReGdN',
	    'Po[DjD R[GiG R[JiJ RXQhQ RZ[e[ R[V[[ RcDcM RcOcT RgVlZ RVXR[ ReXe[ RWNRR RSDXF R]UaW R]RXT RjTfT R]O\\S R[D[MkMjT',
	    'PoVCV[ RdDjDkL RhIeL R^E[H R_I[M RgFdG RYHYK RdOlR R[DaDbM_PZR RcQ\\T RfS^W RiVcY\\[ RSIRO',
	    'Po\\QjQ R_[g[ RVCV[ R\\F\\Q R`U`[ RgXg[ R\\FjFjQ RjVlZ RbCaF RYHYL RcH^KeM RaSdT R\\UZZ RaJfJcN_O RSIRO',
	    'PoSDkD RWIgI RWKgK RZRdR RSNSQ RWGWN R_D^G RWGgGgN RSNkNlQ RgPjS RWPTS R]O`P R[P[SSW R[Xj[ RXU]XaZl[ RXUfUaX\\ZR[',
	    'PoTFjF RUHhH RRKlK RWQ^Q R`QgQ RWTgT RZ[d[ RWMWT RZVZ[ R`OhNgT RgVkZ RVWR[ ReXe[ RaHfMlP R\\UaW R^C\\IWNRP R^MWN',
	    'PoZFlF R_KiK R_MiM R_PiP R[SlS RZ[e[ RWJWT R[V[[ R[I[S R_H_P R_HiHiP RgVl[ RVWR[ ReXe[ RWCSH RXHRN R\\UaW RcCbH',
	    'PoSE\\E RRG`G RTL_L RTO_O RSQ_Q RfTjT RY[f[ RTITO RYVY[ RYDYT RTI_I_O RRTYT_UbX RjVk[ RgXf[ RUWSZ RbS`T RaIlIlOkT RfCfIdQ R^DYD',
	    'Po^QiQ R]UkU R[ZlZ RVCV[ RYHYM R\\H\\L R^L^Q RdCdH RdQdZ R^LiLiQ R\\HlHlL R^D_G RiDhG RSHRO',
	    'Po_GlG RRI^I RbLiL RbRiR RTR\\R RZ[e[ RTMTS RYDYM RZUZ[ RTM\\M\\R RbCbS RiCiS RgVlZ RVWRZ ReXe[ R\\TaV R\\DRE',
	    'PobEjE R`MgM R^McM RbOjO R]ZlZ RVCV[ RYHYL RbCbH RcJcO RcRcZ RgRgZ R\\IkIjK R_S`W RjSiX RSIRO R\\I\\QZZ',
	    'PoZFdF R[JcJ R[RcR RVCV[ RXHXL R[M[R R_C_J R[McMcR RkDfJ RkLfQ RlTeZ R\\T\\X RbT`Y RSIRO RdYYZ',
	    'Po[EkE RZIlI R^NjN R^QjQ RhTjT RZ[e[ RWGWT R[W[[ R^K^S R_C_H RdKdS ReXe[ RfCfH R^KjKjT RhWl[ RVXS[ RVHRL R]UaW RXCUJ R\\I\\L[R',
	    'PoZ[d[ RTHTS RYCYS RZUZ[ R\\K\\O RgVlZ R\\E_G ReXd[ RVDRH RbHeMhQlR R\\TaV RVKVP RUVSZ RdC`L RbHkHgMdQ`R RTH^H^R\\R',
	    'PoUI\\I RSL]L RfSjS RY[f[ RXIXR RYVY[ R^TaW RiUlX RXD\\F RfXf[ RYCSI RUNUQ RbJfK RTVSZ R\\N[P RbC`H RiM`O RaFlFlMjS R^QSS',
	    'Po\\EeE R[HlH R_OgO R_YgY Rg[k[ RVCV[ RXHYL R[Q[[ R_T_Z R_TgTgX R^KhKgO R[QkQk[ ReHjM RbG[M RbE_J_O RSIRO RiDaD',
	    'Po\\KlK R\\OlO R^VkV R^ZkZ RWCW[ R\\G\\O R^R^[ RdGdO R^RkRk[ R\\GlGlO R_DaF RiCgG RYHZL RSIRO',
	    'Po_NhN R[SkS R`[c[ RVCV[ RXHXK RcSc[ R\\K_L RhVkY RfKlP R^UZY R^HaI[P RdEhM RdFkFiJ R^C\\I R]FbF_L RSHRO',
	    'Po[IkI RcQlQ RaWlW RVCV[ RcCcO RcQcW RhOh[ R[T^U R]D_G RjDgG R_VZ[ RYHYL RcHfKlM R]O[T R\\RaQ^X RcH`L[N RSIRO',
	    'Po\\EjE R\\HiH R\\KiK RfTjT RZ[e[ RZVZ[ R^P^S RcEcN RgVl[ RePgQ RUXR[ ReXe[ RWNRR RbPcR RSDXF R\\UaW R[PYS R\\E\\NkNjT',
	    'Po]HjH R[KlK R]QjQ R]TjT R]WjW RWCW[ R]E]H R]N]W RdCdK R]EjEjH R]NjNjW RYGZJ RfYl[ RaY[[ RSIRO',
	    'Po[FlF R\\LjL R[SkS RVCV[ RcCcR R\\IjIjO RhVlZ R\\I\\OiPlT RYHYL RaTeV R\\VZZ R`V`[gZg[ RSIRO',
	    'Po]HlH R^LjL R^PjP R^TlT RVCV[ R^G^T R`Wa[ ReFeT RjWl[ R^HZL RYGYK ReWf[ R_C\\K R\\W[[ RSIRO ReDdJ',
	    'Po_KgK R]TiT RZZlZ RVCV[ RYHYK R]O]T R]OiOiT RfHlM RiDfG RkHgI R\\H_IZN R_V_Y ReDhJ RgVeY R\\EaE^K RSIRO',
	    'PoRG_G R`IlI RZ[d[ RTJVL RYCYS RZVZ[ RgVlZ R\\J_M RhOlS RiDkF RUWR[ RdO_S ReXe[ RYLRR ReCfLiR RYN]O R\\TaV RTHRM R\\H[L ReGhO ReFdO',
	    'PoRG_G RZ[d[ RYCYS RZVZ[ RTJVL RgVkZ R\\J_L RUWRZ RdXd[ RYLRR RbGeNhQlS RYM\\O R\\TaV RUHRM R\\H[L RcC`K RbGjGfNcQ_R',
	    'Po[FlF R\\IkI R]OiO RZSlS R]ZjZ RWCW[ R]V][ RcCcI R]KiLiO R]VjVj[ RgPfS RYHZL R_P`S R]L]O RSIRO',
	    'PoSEkE RUHiH RWMhM RRPlP RVUhU RVRVU RWJWM R_C_G R[WZ[eZd[ RWJhJhM RVRhRhU RgXkY RVXS[ RdNbP R]VaX',
	    'Po\\IbI ReIkI R]OkO R]SkS R[WlW RWCW[ R\\D\\I R]L]S R\\DbDbI RdLd[ ReDeI R]LkLkS ReDkDkI RYHZL RSIRO',
	    'Po[DlD R\\KkK R]QjQ R]TjT RZWlW RVCV[ RXHYL R\\G\\K R]N]T RaDaK RdTd[ RfDfK R]NjNjT R\\GkGkK RSHRO',
	    'PoaFlF ReIkI RcMlM RcSkS RcWkW RVCV[ RXHXL R[D[[ RcPc[ RhIhM ReDaL R`O_U]U R^K_R R[D_D^L RcPkPk[h[ RSIRO',
	    'PoRE_E RbHlH RTJ]J RRP_P RUTYT RZ[e[ RTHTK RYCXF RYOYT R[V[[ RTH]H]K RgVlZ RVXR[ ReXe[ RbHdNhRlS R]UaW RcC_L RTM^MYO RiGfNcQ^S',
	    'PoYGcG R[MbM R[[^[ RUCU[ RXHXL R[I[M R^S^[ R[IbIbM R^C^F RZQbP^T RgUb[ RfRjZ RiPeY RfDcO ReJhW ReJkIhU RSIRO RbUYV',
	    'Po[ElE R\\NkN R[TlT RVCV[ R\\I\\N RaEaN RcOc[ RfEfN R\\IkIkN R]P_S RhPgS RYHYL RcSgXlZ RcS_XZZ RSIRO',
	    'PoTDkD RWIaI RWO`O RZ[e[ RWLWQ R[V[[ R`L`P ReYe[ RcJjIjM RhVlZ RhQkT R[I[QXT RVWS[ RePaT RXFYH R]UaW R_F^H RfJhR RcFbM RTDTMSS RfIeQ',
	    'Po\\FbF RdFlF R[LkL RgUjU RVCV[ R\\O\\U RcJc[ R\\OjOjU R^F_H RfFiH RcSgXlY R]CZH ReCcH RbS]XZZ RXHYL RSISO',
	    'Po\\FjF R[JlJ RZMbM RdMkM RcQlQ RZTlT Rf[l[ RVCV[ RXHYL R^K^Q RcCcJ RfTf[ RgKgQ RlXl[ R`T^YY[ RSIRO RaQZR',
	    'Po[KlK R]ViV R]ZiZ RVCV[ R]R][ R\\EcEdP RcRcZ R]RjRj[ R^G_I RhFgJ RYHYL RcJhOlP RcJ^OZQ RSIRO RiCaD',
	    'Po\\FkF R[JlJ R]PjP R]SjS R]WjW RVCV[ RXHYL R]M]W R_G_M RcCcH RgGgM R]MjMjW RfXl[ R`XZ[ RSHRO',
	    'PocTgT RZ[e[ RSLSO R[V[[ RSLkLkO RhVlZ RXH\\I ReHiI RVXS[ RjEhG ReXe[ RUETH RXGUJ R]E[G RbE`H R\\UaW RVOhOgT RdI`J RYCXI ReCdI R^O[RTT',
	    'Poe[i[ RVCV[ RXHYL RZNZR RZNlNlR R^HbK RhHkL RgIdM RkEjH R\\E[H RbEaG ReEeH R]L[L RaV_YZ[ RbQaX RSHRO R_D^K RhDgJ R\\SjSi[',
	    'Po[FkF R[JlJ R\\QjQ R\\TjT R[WjW RZ[l[ RVCV[ R\\M\\T RcCcF RcMcZ R\\MjMjT RYHYL R_G_I RgGfI RSIRO',
	    'PoSE^E RTK\\K RTN\\N RRRYR RZ[e[ RTHTR RXEXH R[V[[ RgVkZ ReXe[ RaHdMhQlS R\\TaV RVVTZ RcC_L RaHkHgNcQ_S RRH^H\\RVR',
	    'PoRI^I RaJkJ RaNkN RTR]R RaRkR RZ[e[ RTMTR RYEYM RZUZ[ RTM]M]R RaFaR RaFkFkR RgVlZ RUWR[ ReXe[ ReCeF R\\TaV R]DUE',
	    'Po\\HbH RfHlH R\\LbL RfLlL RaRgR RaVgV RdYgY Rh[l[ RVCV[ R\\D\\[ R\\DbDbL RfDfL RaOgOgY RfDlDl[ RYHYL RSIRO RaOaU`Y',
	    'Po\\HbH RfHlH R\\LbL RfLlL RVCV[ RYHYL R\\D\\[ R\\DbDbL RdMdP RfDfL RaPbTeWiY R_PhPfTcW^Y RSIRO RfDlDl[h[',
	    'Po]GjG R]JjJ R[NlN R^UjU R`[d[ RWCW[ R]D]J R^Q^U RdUd[ RdJdN R^QjQjU R]DjDjJ RhWl[ R_W[Z RYHZL RSIRO',
	    'Po[GlG R]PhP R]ThT R_[b[ RVCV[ RXHYL R^K^T RcTc[ R\\MiLhT RgVkZ R[I]K RdHkN RjHgK R^VZZ R`IZO RbC^K RSIRO',
	    'PoZNlN RZTlT RVCV[ R^F^T RaGaT ReGeT R]HjHiT RYGYJ ReVf[ RjWl[ R^C[J R\\VZ[ R`Wa[ RSHRN',
	    'PoeDlD R[JcJ RdJlJ R]VjV R]ZjZ RVCV[ RXHYL R\\D\\J R]R][ ReDeJ R]RjRj[ RgLcP R_L[P RcN_O R`H`N RSIRO R\\DaE_M RiEhIiOlO',
	    'PoaNdN RfSlS R`YgY RVCV[ R[P[[ R`U`Z ReGeN RePeS R`UgUgY R[PlPl[ R[IlIiL RYHYK R`FfI R]EiEcG RbJ[M RaP`S]T RSIRO Rh[k[h[',
	    'PoYGcG RcJlJ RZQbQ Rh[l[ RVCV[ RXHXL RZKZQ RZKbKbQ RiNiZ RlWl[ RiElG RaTbW R]C^G RfSb[ R[TYY R^Q^[[[ RSHRO RgCgMeW',
	    'PoVHhH RVKhK RRNlN RVTiT RZ[e[ RSESH R_C_F R_G_N ReYd[ RSEkEkH RhWl[ RVWR[ RZWZ[ R\\UaW R\\P\\T RbPbT RVPiPiT RVPUT',
	    'Po[HlH R]KiK R[NbN RdRkR RZUlU Rc[g[ RVCV[ R^N^R RdNdR RgSg[ R]DjDjK RdNkNkR R^VaY RYHYL RSIRO RbQZR',
	    'Po[I`I RdIjI RZM`M RZQ`Q RdQkQ RZV`V RdVkV Ra[k[ RVCV[ RXHXK RZMZV R[E[I RaEaZ RdEd[ R[EjEjI RdMkMkV RlXl[ RSIRO',
	    'Po`FkF RTI]I R_KlK R_OlO RUR]R RY[f[ RTDUMUR RYUY[ RTD]D]I RUL]L]R ReKeS ReCeF R^TbX RiUkZ RfXf[ RcGcJ RUVSZ RhGhK RTISR',
	    'Po\\FkF R[JlJ R]PjP R]SjS RVCV[ R]M]S RcCcF RaV_[h[hZ R]MjMjS RbUeV RhVlZ RYHYL R_F`I R\\VZ[ RhFgI RSHRO',
	    'Po[FbF RdFkF RcUjU Rb[l[ RUCU[ RXHXL R_C_N RcPcZ RgCgN R^FaJ RjHkL R[PjPjS R_E\\K RgEdL R]S\\Z RSIRO',
	    'Po^JgJ R[RaR RdRjR RVCV[ R[M[R R[MaMaR RdMdR RdMjMjR RhXk[ RfWiX R_WaY R^WZ[ RfWb[ R]IYJ RbC\\I RYGYI RcChHlJ RgTdZ R^T\\Y RSHRN',
	    'Po\\IjI R\\LjL R[OlO R^QjQ R[UlU RVCV[ RXHYL R\\E\\L R_T_Z RaCaL ReCeL R\\EjEkL RdVgYl[ RkVfX RSIRO R[O[SZZ ReY\\[',
	    'Po\\IlI RfLlL R`NjN R\\PlP R`RjR R`TjT R_ZkZ RVCV[ R_W_[ R_WkWk[ R^FYK RbJ^M RYFYJ R_EfEeHfL RSHRN R_C\\I\\R[Z',
	    'PoZFlF R_MfM R\\PjP R]UiU R]XiX RZ[l[ RVCV[ RXHYL R\\H\\O R]R]X RcCcF R_K_LfKfM R]RiRiX R\\HjHjO RSIRO',
	    'PoRE`E RUPZP RZ[e[ RULUP RZUZ[ RULZLZP R\\C\\G RgUlZ ReXe[ RWCWGRK RbGdMhPlR R\\TaV RUUTY RcC`J RbGjGgMdP`Q R]RZR RTJ^J]R',
	    'Po[FlF R_[i[ RVCV[ RYHYL R_U_Z R`D`H RgDgH RcUfW RaM\\Q RjUkY R]H[M R\\UZZ RaJ_O RfJcR RiXi[ R]MgP RiRfR RSIRO R]JjJiS',
	    'Po[GkG R]JcJ R^QcQ RVCV[ R^M^Q R^McMcQ R`V`[gZg[ RhVlZ RhDkE RgOdS RXHYL RaTeU RiJfR R\\VZ[ RlQkTiT RSIRO ReEgQ R[G[NZT',
	    'Po[JbJ R[NbN RYQbQ RVCV[ RXHXL R[F[M R^C^F R[FbFbM RgVc[ RfQjZ RiQeY Ra[][ ReKiX ReJkIhV RSHRO R^O]Q[Y RfCdN R\\UaUa[',
	    'Po\\QjQ R]WiW R]ZiZ RVCV[ R\\K\\Q R]S][ RcKcQ R]SiSi[ R\\KjKjQ RcChH RXGYI R_M_O R_IgHlJ RgMfO RbC^HZJ RSIRO',
	    'Po\\IiI RZLlL R\\SiS RVCV[ R\\D\\I R\\N\\S R_V_Z R`D`I ReDeI R\\NiNiS R\\DjDjI RYGYK RdUhYl[ RbTYX RjUfW RSIRO RfZ\\[',
	    'Po[FlF R[KlK R\\QjQ R\\UjU R\\WkW RZ[l[ RVCV[ R\\N\\T R`C`G R\\IdId[ RgCgG R\\NjNjT RYHYK RSIRO RjHcI',
	    'PoRE`E RVG\\G RTL_L RSO_O RTQ^Q RXS_S RfTjT RZ[e[ RTJTL RVCVG RYGYS RZVZ[ R]C]G RTJ_J_L RaHkHkR RgVl[ RVWR[ ReYd[ RST\\UaW ReCeJdP',
	    'PoYFdF R\\IbI RZPcP RZScS RZVcV Rg[j[ RUCU[ RWHXL R\\C\\I RbCbI RZLcLcP RZLZP RSHRO ReJlJlUk[ RhChOfY R_I`YZZ RcY]Z',
	    'PobEjE RbNjN R]PjP RVCV[ RXHYL RbCbM RhVlZ R[HkHkK ReP^S RdS]V RdReVcW]Z RjReTfY Re[b[ RSIRO R[H[SZY ReK^K RhJaK',
	    'Po^IhI R[PbP RePkP RZTlT RVCV[ R[K[P R^D^I R[KbKbP RcRc[ ReKeP R^DhDhI ReKkKkP RYGYK RcShXlZ RcS^XZZ RSIRO',
	    'PoaHjH RaLjL RZ[e[ R[V[[ RaEaR RaEjEjL RTGUI RgVlZ RfNjS RVXR[ ReXe[ ReLgP R]UaW R[KSO R[NSR RkMfO R^GTK R[J]P\\SXS R\\DRF RfR_R',
	    'Po[ObO ReQlQ R[TbT RcUlU R_[b[ RVCV[ RXGXJ R^J^T RZKbKb[ RhMh[ R\\FaF^K RgEeK ReMdS RjKhK RSHRO R]DZJ[TZZ RdEkEjL',
	    'PoTFlF R_JkJ R_LjL R_OjO R_RlR RZIZS R`I`R R`C`F ReHeR R^V][fZf[ RhVlZ RYWVZ RZIVN R_I[M R_TcV RTFTSS[',
	    'Po^LhL RZTlT RVCV[ R\\F\\S RdHdQ R\\FjFjR RcL^Q RXHZL RcNhP RcSdWfYl[ RbCbF RfIfK RcSaW_YZ[ RSIRO RaJ`L RgG`H',
	    'PoRHYH RdHlH R\\R_R RZ[e[ RVCVS RZVZ[ R_J_R RhChS RVIYL RgVlZ ReXe[ RZEbE^H R]LYQ RhHlQ R\\G`I R]UbW RUVTZ R^J\\N RZJcJaL RgGcP RVGSP',
	    'Po\\IkI R\\LjL RZPlP R[TlT R[WjW RVCV[ R\\D\\I RaDaI RcJcO RcTc[ RfDfI R\\DkDkI RYHYL R_P`T RgPfT RSIRO',
	    'PoZFkF R`MfM R\\OjO R_RfR RZUkU RVCV[ R\\H\\O R`J`M RcCcF R\\RcRc[ R`JfJfM R\\HjHjO RYHYL RcTfWlZ RcT`XZZ RSIRO RhQbR',
	    'Po[FkF RZNlN R`XfX R\\ZjZ RVCV[ R\\Q\\[ R`T`W RcCcN R`TfTfW R\\QjQj[ R^IaL RhJkM RYHYL R^H\\M RhHfL RSIRO',
	    'Po]GlG R]JkJ R]MkM R]PlP RaWfW Rh[l[ RVCV[ R\\S\\[ R^F^P RdEdP RfSlSl[ RYHZL R^C[J R\\SaSaW RfSfW RSIRO',
	    'Po[FkF R\\OkO RZRlR RVCV[ R\\I\\O R`U`Z RaIaO RcCcI RfIfO R\\IkIkO RXHZL RcQeViZl[ RcQ`UZW RjTfV RcOdU RSIRO RfY\\[',
	    'PoZElE RYIlI RgTkT RZ[f[ R[V[[ RZLZT R_C_G RfCfG RZLkLkS R_NbQ RhVlZ ReNhQ RfYf[ RUQSS RRIVJ R^UcW RVNUR RUWT[ RSDXE R`I^Q RfIdQ',
	    'PoZGbG RZQbQ RUCU[ RXHXL RZKZQ R^D^[ RZKbKbQ RdJdU RdJkJkU RhXl[ R]QaV R[UYX RgWc[ RfFkFhJ R^P[W RfCcJ RSHRN RhMhRgX',
	    'Po[EdE R`QgQ RVCV[ RXHYL R[J[M R[JkJkM RhMkP R^TZV R`R\\V R]M[P R`M`Q R]F]I RbFbI RaLeM RiFgJ R^TbXfYl[ R^ThTeWaYZ[ RSHRO RjCbD',
	    'PoYFlF R_MfM R[PjP R\\RiR RYUlU R^[b[ RVCV[ R[H[P RcUc[ R_K_LfKfM R[HjHjP RiXlZ R\\XXZ RXHYL RbCbF RSISO',
	    'PoTElE RWK_K RaKlK RWO_O RWQ_Q RZ[e[ RWMWT R[V[[ RhVl[ RhPlT RiGkI RVWR[ ReXe[ RfJiR R]UaW RcRaS R\\I_I RWGWJ[I RWG_G_J[I RWM_M_T\\T RTETMST RfGeQ',
	    'PoUI\\I R`IlI RSL]L RaRkR RY[f[ RYVY[ RYIYR RaMaR RgEgM RaMkMkR R]TaX RiVkZ RfXf[ RYDTI RUNVP RXE]G RTVSZ RjDaE R[RSS R\\N]QVR',
	    'PoZGaG RdGkG RYKbK RcKlK Rf[i[ RUCU[ RWHWK RhChKi[ R`OaT RkOlU RZOYU RdOcU R^C^K_[\\[ RSHRN R\\K\\Q[Y RfKfQeX',
	    'PoYFlF R`IdI RhJlJ RfShS RZ[f[ RZVZ[ RZLZO R\\O\\S R`C`I RbLbT RdCdI RhChJ R\\OhOhS RZLkLkO RTDWE RhVkY RfXf[ R\\GZJ RVNRS RRIVK R^VbW RUVSZ R\\D\\I',
	    'PoaGeG R\\J_J RgJjJ R\\O_O RgPjP RYUlU RVCV[ RXHYL R\\E\\U RaOaU RaCaKeKdU RgEjEjU RiXk[ RfWjY R]YYZ R_W\\Z R_D[F RSIRO',
	    'Po`EfE R\\IjI R[LlL R\\QjQ R\\TjT RVCV[ RXHXL RcIcL R\\NkNkT RbUeX RiVlY R]F^G RhEgI R[EcEcH R_V_[hZh[ R[WYZ RSIRO RjCbD',
	    'PoZElE R[GjG R]LiL R\\QiQ R\\TiT R[WiW RZ[l[ RVCV[ RXHYL RZNYQ RcCcG RcTc[ R]J]KiJiL RZNlNlQ RbQ\\T RSISO',
	    'PofFlF R\\MkM RVCV[ RZPZS R\\I\\M R[FaEaG RaIaN RfCfH RfIfN R\\IkIkM R[PlPlS RYHZL RbS[V R`TiTeW^Z RSIRO R^UdX[[',
	    'Po[KbK R[OaO RgSkS RZTbT RVCV[ RXHYL R\\C\\J RcVgZ R^N^UaX RdHhI RcEkEgI R]WY[ R^T\\Z R\\LZR RaE[G RSHRO RdQbZ RhMgZlZ RcMkMkQ R^P]X',
	    'PoZElE R]LhL R[OkO RVCV[ RXHYK R[I[L R_C_H RfCfH R[IkIkL RfTkY RdOgW RcO\\S R`QcT[V RbVZZ RjSfT RcSdXd[`[ RSIRO',
	    'PoRHaHlI RSL_L RTO^O ReThT RZ[e[ RWCWH RYLYS RZVZ[ R[C[H RiCiT RgVlZ RVXR[ ReXe[ R^D^F RTDTF RVIVK RbLdP RRTXS]TaW R\\J\\L',
	    'PoZFlF R\\WiW RVCV[ RcJcO RbCcF RfJfO R]RiQi[ ReGcJ RXHYL RiIkO R\\JZO RkIfJ RiOeO RSISO RZJ`J_O]O R]P]U\\Y',
	    'Po[FlF R]JcJ ReIlI R^ZkZ RVCV[ RXHXL R]K]P R`G`P RbKbO RcCcF RgGgP RjKjP RgWl[ R^TbU RcQ`T RgSaY ReKdP RSIRO R[F[SYZ',
	    'Po[EcE ReIlI R[LcL ReOlO R[PcP RY[l[ RVCV[ RXHYL R[E[P R\\S\\Z R_E_H RaSaZ R[HcHcL ReSeZ R\\SjSjZ R_L_P RfDeK RSIRO',
	    'PoYElE R^IfI RgTjT RZ[e[ RZVZ[ RZKZT R^C^I RbIbS ReXe[ RfCfI RZKjKjT RgVl[ RVWR[ R_M`R RfMhR RTDWE RRIVJ R\\UaW RfNdR RVNUR R^N\\R RfMdM R^M\\M',
	    'PoZElE R[HjH R\\MiM RZOjO RYRlR RbUlU RZY`Y Re[i[ RVCV[ RZTZZ RZT`T`Y RbMbP RcCcH RiSi[ RcWeY RXHYKlKkM RSIRO',
	    'Po\\EkE R]NiN R[TkT RcWjW Ra[l[ RVCV[ RXHYL R[H[K RcTcZ RcKcP RcCcH R]KiKjP R[HlHlK R]K]PiQkU RaP^S R^WZ[ RSHRO',
	    'Po\\EkE R^JbJ RfJjJ R]MbM RfMjM R\\PlP RVCV[ RXHYL R\\H\\K R]T][ RbSb[ RdEdN RfTf[ R\\HlHlK RbPaW R]TkTk[i[ RSHRO',
	    'Po`LgL RZSlS R[UlU R_[c[ RVCV[ R[F[I R]N]S R`I`L RaNaS RcCcF RcUc[ RfNfS R]NjNjS R[FkFkI RiIkL R]J[L RYHYK RaHeI RSIRO',
	    'Po[ElE R]JcJ R]McM ReMlM R]ScS R]VcV RaZcZ RVCV[ RXHXL R]P][ R]G]M R]GcGcM R]PcPdZ RjHkK RSIRO RhGhPfZ RhLjY R[EZSY[',
	    'Po[FkF R\\IjI RZLlL R`NfN R]ShS R]UhU RVCV[ R]P]Z RcFcL R]PhPhU ReLkR R^C_E RgDfE R`LYR RYHYK RbUeXk[ RSIRO RjWeY\\[',
	    'Po\\GiG R[NjN RZQlQ R_[b[ RVCV[ RYHYL R_J_R RcSc[ R\\DiDiJ RgUeW R\\D\\JhJfRhTlU RcVjY R^SXU RcVZX RSIRO',
	    'PoeGlG R^QjQ R^TjT R^WjW RVCV[ R^N^W R]GbGaL RiGiL R^NjNjW RXHYL RgYl[ RaY[[ RSIRO RbD]E\\L RkDeEeL',
	    'PoUElE RWHkH R\\JfJ RWLlL RYPjP R\\G\\J R`C`E RaLaR RfFfJ RYNjNjR RhWl[ RVXS[ R]VaX RZWZ[eZe[ R]TVU RYNYRgSkU RUEUNST',
	    'Po[FlF R[IlI RaLhL R[NlN R^TjT R^WjW RVCV[ RXHYL R^Q^W RaFaL RcCcF RdNdW RhFhL R^QjQjW RgYl[ RaY\\[ RSIRO R[F[QZZ',
	    'PoZDlD R]IiI R]LiL R`SfS R^VhV RVCV[ RZNZQ R]G]N RbDaG R]GiGiN RZNlNlQ RhPkS R]Q[S RXHZL R_P`S^VZW R_WdYk[ RSIRO RgWbZZ[',
	    'PoRE_E RSJ^J RSL^L RRO_O RZ[e[ RSHSM RYCYM RZWZ[ RSH^H^M RgVkZ RdNjT RVWS[ RVR]R\\U RgNaT RWQRU RbHfQ RZU]UaW RbHkHeQ RcC`L',
	    'Po]QdQ RZTfT Rh[l[ RUCU[ RWHXM R[E[L R`Q`Z RhGhU RlCl[ R\\V]Y R`MgP R^G_JZK R`L^OYQ R_D[E RcEaK ReKcK RSIRO RaDfEeL RdVcY[Z RfY_Z',
	    'PofFlF R\\QlQ RVCV[ RYHYL RaIaN R\\FbDbH RfCfH RfIfN R]IkIkN RlXl[ RfWi[ R]I]NiOjP R]UaV RiSeX`Z RdNeX RSHRO R\\Q[Z',
	    'PoZHaH RYKbK RZNaN RZ[e[ RVJVT RZEZH R[V[[ R]C]H R]K]R RaEaH RgVl[ RgPlT RVWS[ RhOcT ReXe[ RWDSH RWIRN RfJiR R]TaV RjJfQ ReGgP ReHkGhP RfCcL RbQYS',
	    'Po[EbE RdElE R[HaH RdHkH R[KbK RcKlK R\\QiQ R[TiT RVCV[ RXHYM R^C^L RhChL R^V^ZhZh[ R\\NiNiT RbUdX RiVkX R[VYZ RSIRO',
	    'Po[FdF RZJlJ RZN]N RZQ]Q RYT]T Ri[k[ RVCV[ RXHXL R]J]Y R_C`JaX RlXl[ RiDjH RgVb[ RjOfX RaNdN`N RaQdQ`Q RaTdT`T RSIRO RgCfOhZ RdWYY',
	    'Po[ElE R\\LkL R]TiT R]WiW R]ZiZ RVCV[ R[O[R R\\H\\L R]R][ R`D`G RgCgG R]RiRi[ R\\HkHkL R[OlOlR RXHYL RaHaL RfHfL RSHRO',
	    'PoZGbG RdOkO RZQaQ RdSkS RdVkV RVCV[ RXGXK RZKZQ R^C^[ RZKaKbQ RdLdV RdLkLkV R]QbW RiXl[ ReXb[ R^PZX RfDdJ RkJhJ RSHRN RcDkDkJ',
	    'PoZFlF R\\LjL R[O`O RfOkO R[R`R RfRkR RVCV[ RXHYL R\\H\\L R_U_Z R`HaM RcCcF RcNcQ ReHeM R\\HjHjL RcRdVhYl[ RcSZW RjTeV RSIRO ReY[Z',
	    'PoWG]G RWI]I RWL]L R`NkN RTO_O RZ[e[ RTFTN RWDWL RYOYU RWD]D]L ReYd[ RfNfU RhWl[ R\\Q^S RiQlS RaHeK RiLlO RVWR[ RhHbN RcQ`T RVQST ReEbI RZWZ[ R]UaW RjD`E',
	    'PoZFlF RdLjL R[NkN R[QjQ RZTlT RVCV[ RXHYL R^V][ R\\L`L_T R\\HaHaL RdHeLfT RdHjHjL RbCcF RbTfYl[ RjVfX R\\H\\L RcT^WXX RcZ][ RSIRO',
	    'PoYLlL RZR]R RYU]U RVCV[ RXGXK R]L]Y RaLaY R\\F_H RbGdI RiEkH R]DZJ RbD`J RZO]OYO RaOdO`O RaRdR`R RdUaUdU RSIRO RgCfOhZ RjOgWcYYZ RlXk[i[',
	    'PoeJkJ R\\NjN R\\QjQ R[TkT RZWlW RVCV[ R\\K\\Q R`Q`W RcKcQ ReDeI RfQfW R\\KjKjQ RXHZL RfYk[ R`Y[[ RSHRO RkEeF R[EaDaIZI',
	    'Po\\GbG ReGkG R\\JbJ ReJkJ R\\LbL ReLkL R\\QkQ R]SjS R]WjW R]ZlZ RVCV[ R\\D\\L R^O^[ R\\DbDbL ReOeZ ReDeL ReDkDkL R_NZS RYHZL RSHRO',
	    'Po[ElE R\\MbM ReMkM R]QkQ R]SjS R]WjW R]ZlZ RVCV[ RXHYL R\\I\\M R^P^[ R`C`G R\\IbIbM ReOeZ RgCgG ReIkIkM R_NZS RSIRO ReIeNdT',
	    'Po[DkD R^GhG R^JhJ R[MkM R[RaR ReRjR R[UaU ReUjU RcXlX RYXaX RVCV[ R[P[X R^D^M RYPaPa[ RePeX RhDhN RcPkPj[ RYGYK RSIRO',
	    'PoRF]F R_FbF RSI]I RUN[N RTU\\U Rb[h[ RRLRO RTQTU RXCXI RTQ\\Q\\U RRL]L]O RcUc[ RhYh[ RdHkGkK RcRfT RhMlQ RfMbQ RVVVY RjUlZ ReCbJ R`V^Z RcL^M RgGgN RZVZYSZ R\\YVZ',
	    'Po\\DbD R[GcG R\\IbI R\\LbL RdLlL RRLZL R\\QbQ RZ[d[ RWLWS RZVZ[ R\\N\\R R\\NbNbR RiLiS RXJZL RgVlZ RdFgI RUWRZ RjFdL ReXd[ RgCfG RUCSGVHSL RXNZQ RkNkR R]TaV ReNdR RSNSR',
	    'PoRE`E RbHlH RVG]G RbKkK RTL_L RSN_N RbOkO RRQ_Q RbTlT RZ[e[ RTITL RVCVG RZVZ[ R]C]G RTI^I^L RcFcU RgEgS RgVk[ R\\UaW RVWR[ ReXe[ RXQ^T RYGYQVTRU RcCbK RgDgI',
	    'Po[FlF R]IcI ReIlI RfRkR RfUkU R]UbU R\\XbX RfXlX RVCV[ RXHYL R`G`O R]RbQb[ RfQf[ RhGhO R_IcM RgIlN RhJdN R`I]O RSIRO R[F[SY[',
	    'Po]LhL R\\RjR R[TjT RZWlW RVCV[ RXHXL RZGZJ R\\N\\R R`Y`[ RcCcG RcNcW R]I]JhIhL R\\NjNjR R[GkGlJ RiXl[ R^D_G R\\YZ[ RhDgG ReYe[ RSIRO',
	    'PodFjF R[JbJ RdJjJ R\\OjO R\\QjQ R\\SkS R[VjV RVCV[ RXHYL R]L]S R[DbDbJ RdLdS RdDjDkJ R^JYO RgHkH R_H[H R[FbG^H RhHdH RdDdIhH R[D\\I_H R^WcYl[ RSIRO RiWdYZ[',
	    'PoaEkE RSF^F R`HlH RRI_I RaMkM RTM]M RaOkO RTP]P RaRkR RRR_R RTKTP RYPYU RTK]K]P RaJaR RfEfH RY[fZf[ RaJkJkR RVGVI RiWkZ RXCXF RhSlU RUWRZ RcSaV R]V`W RYWY[',
	    'PobEjE RSF^F RRI^I RbJjJ R`LkL RTN]N RTP]P RRS^S RY[e[ RTKTP RXPXU RTK]K]P RaOaS ReJeM RaOjOjS RiXj[ R]V_X RXCXF RUXR[ RcC`F RXXX[ RUGVI RgTkV ReQdU`V RcFlI RhG_I',
	    'PoSF^F RRI^I RbIjI R`KkK RSM]M RSP]P RRR_R RZ[d[ RSKSP RXDYF RXPXU RZWZ[ RSK]K]P ReYd[ ReIeK RaMjMkS RhWk[ R\\V_W RVWR[ RUGVI RhTkU R[FZI RdT`U RdD_F RgOkO RbEfFkH RhF`H RdOaOdO RaMaSdSgS RfScS RaQjRgS',
	    'PobJjJ R_JhJ R]JfJ R[KcK RXK`K R^NbVeZi[ RbDhG RiMbTU[ RRKWK]JaU RlWk[h[ R\\D_R',
	    'PoVIkI RVIVW RcVh[ RaQeY ReDjF RiLfRaX[[ R^URX RlVk[h[ RaC`JbV',
	    'PoUIkI RdWfZj[ ReDjF RhLfQ`XY[ RlWk[ RaCaIcV RUIURTY',
	    'Po]JiJ RYJeJ RWJbJ RaOlO R^OiO RSP[P`ObWeZh[ RaEfG RTJ^J`U RhQeU^XU[ RlWk[h[ R]E_R',
	    'PoUHlH RXP`P RfYj[ ReDjF RlVk[ RiLfR`Y[[ RbCbJdW RUHURTY',
	    'PoUHlH RYP]S RUNZP RfYj[ ReDjF RiLgR`Y\\[ RlVk[ RbDbJdW RUHURTY',
	    'PoRIlI RdWh[ RXWS[ RfR^Z ReDjF RiLeT RbNeX RlVk[h[ RbCaJcV RYKYRXW R_QRR',
	    'PofKlK RgDjG RgS`Z RSJVMXRRX RYQ\\W RgZj[ RiMfT RlWk[ R[KWU RSG\\GYR RcCbIeW RdK^K RiJbK',
	    'PoUHlH RYW\\W RUN\\N\\W RfDkF RiLfS`Y\\Z RUHUSSZ RbCbIeXk[lV',
	    'PoRLlL RT[Y[ RYEY[ RfDjH RiOaX]Y R_QRS R^DRF RbCbJeXk[lW',
	    'PoRHkH RRP`P R\\J\\Y ReDjF RVJVRUW RcCcNfYk[mV RjLeU`[',
	    'PoRHlH RTU]U RTMTV RTM]M]U ReWi[ RcRgZ ReDiF RiLgPaWX[ RbLeX RlWk[ RbCbIdU',
	    'PoaGjG R_GhG R\\GfG RZHcH RaRkR R\\SfS RWSaS R[C^J RlLkN RlXk[ R[O^V R`OeP RaDfD RSHYH^GaLiN RRS]SaYeZj[ RfJ^LSO RhTbX]YS[',
	    'PoTKZK RRQZQ RTDTK RZCZ[ RdWh[ RfDiH R`X\\Z RgR_Y RbRfY RhMfS RlWk[h[ RbCaJcV RTQSZ RbK\\L RdK^K RhKaK RkJeK',
	    'PoRHkH RTS]S RTLTS RTL]L]S ReWh[ ReDjF RiLfR`X[Z RlVk[h[ R^VRX RbCbJdW',
	    'PoXU[U RU[][ RUMUZ RXE]I RgEjH R]W][ RhTa[ RVHRL RjNgV RXCVI RlWk[i[ ReCeKgY RUM\\M[U RkJgKcK',
	    'PoWI^I RRY]Y RRPR[ RWCWP RRP]P]Z RgEjH RiRaZ RjMgT RlWk[i[ RdCdJfX RjK`L',
	    'PoTI[I RdKjK RbLhL R`LfL RUUZU RRER[ RUMUV RUMZMZU RgEjH RRE]E][Y[ RlWk[i[ RjMhSaZ_Z RdCdJfX',
	    'PoUHlH R\\LaL RWQbQ RY[\\[ R\\J\\[ R`SaW RfDkF RXSVW RUHUSSZ RdDdMfYk[lV RjMgUb[',
	    'PoRElE RVKiK RVNiN RVPiP R\\TkT RSTaT RVHVP R^E]H RVHiHiP RlYl[ RWT_TeShS R[P]UaYi[ RgV`YU[ RcXS[',
	    'PoRHlH RUO_O RSR`R RTU_U RUJUO RUJ_J_O RgZj[ ReDjF RlWk[ RcCcJeW RYRZYSY RiLfT`Y^YYY',
	    'PoSJ\\J RSO\\O RST\\T RSDST RSD\\D\\T RZV\\Z RgDjG RgS_Z RgZj[ RiMfT RUVS[ RlWk[ RcCcIeW RcK^K RiJcK',
	    'PoREkE RVKgK RVMgM RSUjU RRPRS RVHVP R^E]H RVHgHgP RRPkPkS RkYk[ RaReR R[T_XdZj[ RgV`YV[ RcXR[',
	    'PoRG_G RTN^N RTR^R RRV_V RTJTR RYCYJ RYRY[ RTJ^J^R RfDjG RhR`Z RjMgT RlWk[i[ RdCdJfX RhKaK RkJdK',
	    'PoRF^F RdKkK R`LfL RRM_M RTU]U RTZ]Z RTPT[ RUHUK RYCYF RTP]P][ RgEjH RhRaZ RjMgT R\\H[K RlWk[i[ RdCdKfX',
	    'PoRFaF RVJ^J RVM^M RRQaQ RTY`Y RTQT[ RVCVQ R^C^Q R\\U`T`U RgDkH RgTa[ RjNfV RXQWU R\\Q\\U RlWk[i[ RdDdJfX RgK`K RkJdK',
	    'PoTI]I RTP]P RTT]T RSX]X RTDTI RTLTX RRL^L][ RTD^D^I RgDjG RgT`[ RiNfV RlWk[i[ RdCcJfX RfK\\L RkJcK',
	    'PoTDXD RaLhL RUN^N RSS`S RYT^Y R]KaN RgEjH RXVT[ RgT`[ RTGVJRN R`H]K R^E[G^M RjMfV RXFUM R[C]K RdCdJfX RlWk[i[ RYOXW RkKdK',
	    'PoSSS[ RWSWZ RgDkH RhTa[ RWM[MWQ RjMfV RVJ\\JZO RlWk[i[ ReCeLgY RVEUN RSE\\E[H`H_Q RlJdK R[S\\YTZ RSS_S_YVZ',
	    'PoTH_H RTL_L RTQ`Q RTU_U RTDTL RZNZZ RTD_D_L RhEjH RhTaZ RjNgV RUNRT RlWk[i[ ReDeKgY RjKcL R^YU[',
	    'PoSN_N RSR_R RRV`V RSJSR RXDYG RYJY[ RSJ_J_R RgDjH RSDTH R_D]H RjJbK RdCdKgYk[lW RjNfUaZ',
	    'PoWH^H RUN_N RUR_R RWZ_Z RUKVUW[ RUK_K_Q RWT_T_[ R[D`I RgDkH RgUa[ RZCSK RjMfW RlWk[i[ RdCdJfX RUOTX RjJbK',
	    'PoTLZL RRP_P RTWZW RY[][ RTSTY RTHTL RTSZSZW RTHZHZL RRE^E][ RfEiH RgUa[ RjMfV RlWk[i[ RdCdKfX RhKaK RkJdK',
	    'PoSE`E RZL`L RRLYL RRO`O RTV_V RTZ_Z RTRT[ RTR_R_[ R_J`M RhEkH RhSaZ RXJXM RjNfV RUGSL R\\GZL RlWk[i[ ReDdKgX RhKbL RkKdL',
	    'PoTF`F RRKlK RUP`P RUR_R RUV_V RUY`Y RUNU[ RZCZK R[N[Y RgDjG RgT_[ RUORR RgZj[ RiMeV RlWk[ RcCcKeW',
	    'PoSH]H R_HlH RUP\\P R_RdR RUT\\T RY[\\[ RVJV[ RUM\\L\\[ R_K_R R_KdKdR RiDkF RgUa[ RlWj[k[ RjMfW RWGRP RWCTN ReU^V RgCgPiZ',
	    'PoRE_E RRN`N RTU^U RTY^Y RTQT[ RWEWN R[E[N RTQ^Q^[ RgEjH RhTbZ RSGUK RjNgV R`G^L RlWk[i[ ReCeJgY RjKbL',
	    'PoTJ^J RSQ_Q RSU_U RRFRI RSMSU RYJYU RSM_M_U RRF`F`I R[W_Z RXCYF RgEjH RWWS[ RgUa[ RjMfW RlWk[i[ RdCdKfX RgK`K RkJdK',
	    'PoSEXEYM R[E`E`M R]P`S RhDkH RhSaZ RUFSI RVJSM R]F[H R^I[L RYN^P RjMgU RYNURRS RZRUT R\\TUW R^WYZT[ RjJcK ReCeLgY RlWk[iZ',
	    'PoZE`E RYQYZ RZCZH R]Q]Y RTIaI`K RhDkH RVSWW R`S_W RTHTTR[ RZJZP_P_N R^LYLUL RjKbK RU[_Z RkNgVc[ ReCeLgZk[lW',
	    'PoSHXH RZH_H RSO_O RSS_S RRV`V RSDSH RSKSS RSDXDXH RYKY[ RZDZH RSK_K_S RZD_D_H RgDkH RhS`Z RjMfV RlWk[i[ RdDdKfX RgKaK RkJdK',
	    'PoYE_E RYN_N RVUYV RVRVU RZIZN RYPYY RYCYH R\\P\\Y R_R_U RTH`H_K RgDjG RhSa[ RjMfU R\\U_U\\U ReCeKgY RTHTTRZ R]KVL RkJcK R`XUZ RlWk[i[',
	    'PoZEaE RYN`N RVPaP RWV`V RWRWV RZIZM RZCZH RWR`R`V RTHaHaK RhDkH RXWXY RkMgUaZYZ ReCeLgY RTHTTRZ R\\KVK R^JXK RjJcK R]W^ZV[ RlWk[i[',
	    'PoUP`P RUS_S RUV_V RUZbZ RUOU[ RSDXDXL R[N[Z RZD`D`L RgDkH RgTa[ RUFSH RVJSM R]FZH R^I[L RjMfV RVMTR RlWk[i[ ReCeLgY RjJcK',
	    'PoTFaF RRIkI RTNaN RTQaQ RSTaT RRWbW RTKTQ RWQWV RZKZQ RZCZI R]Q]V RTKaKaQ R\\X^Z RfDiG RWXT[ RdCdLgZk[lX RjMgVc[',
	    'PoTGXG R[G_G R[J_J RTLXL R[L_L RTQ`Q RUS_S RUV_V RUZ`Z RTDTL RUOU[ RTDXDXL R[O[Z R[D`D`L RgDkH RgUa[ RUPRS RjMfW ReCeKeMgY RUMUT R[D[MZS RXISJ RjJcK RlWk[i[',
	    'PoWSlS RWJlJlT RWFWRVX R`EWG RjCYF',
	    'PoWOlO RWHlHlQ RaCbG RWHWSS[ RWLVW',
	    'PoSElE RVRiR RVJiJiS RVJVRS[',
	    'PoUE_E RULjL RY[k[ RUHjHjL R\\VYY RlWk[ RYPhP[V RUEURTY RaD\\E RhC`D',
	    'PoTO^O RaOkO Ra[k[ RTG^G^P RbEbZ RaEkEkP RlXlZ RWCYF RTGTSS[',
	    'PoSEkE RVMiM RURkR RVHiHiM RaRbVeXk[ R_U\\YV[ R`N_U RVHUQTX',
	    'PobElE RTJ\\J RTR\\R RbRjR RTJTR RbEb[ RbJjJjR R[UXYS[ RRE\\E\\Q[V',
	    'Po^EdE RUE`E RUMiM ReNe[ RUHiHiM R\\OaR RYR^U RUEURTY RhDaD R^WWX RaWYX ReW]W RjVcW RlVfV',
	    'Po^EdE RUE_E RUMiM RUSkS RUHiHiM RdOhQ RaRbVeXk[ R_U[YV[ R`M_V RUEURTY RgD`D',
	    'PoSEkE RVMjM RVQlQ Rb[g[ RVHjHjM R]VZYV[ R`M`Q R^Q\\W R]UiUh[ RVHVQTX',
	    'PoSE^E RbMmM RTP^P RTI^I^P RjMj[ RkDbG RTITTSZ RbGbMaU^[',
	    'Po`DfD RVEaE RVMkM RXUlU Ri[l[ RXQX[ R_Q_[ ReQe[ RVHkHkM RXQlQl[ RVEVQTY RiCbD',
	    'PoUE^E RUMkM RaQlQ RYZkZ RYUY[ RaNaT RUHkHkM RYUkUk[ RUEUQTY R`E[E RiCaD',
	    'PoUEaE RUMkM R]XgX Rh[l[ RXPX[ R]T]Y R]TgTgX RUHkHkM RXPlPl[ RcD^D RUDUPSZ RfDaD RiCcD',
	    'Po[EaE RUF\\F RULkL R^WgW Rh[l[ RYPY[ R^S^X R^SgSgW RUHkHkL RYPlPl[ RbN`P RUEUQTY RiC_E',
	    'PoUIkI RaTlT RUFkFkJ R^ObP R_CaF R`KXO R]MhMbQYS RcSYV RkUeX]Z RfXX[ RUFUSSZ R`VdXV[',
	    'PoUE_E RUMiM RWQkQ R\\T\\Z RUHiHiM R`PcUfYl[ R`P^SVV RjSdV RUEUQTY RgC`D RbYX[',
	    'PoSElE RVLjL R[[_[ Rg[j[ RXP_P_[ RbPkPk[ RYR\\T RdRfT RVHjHjM R]WWX RhWbX RVHUQTX',
	    'PoSElE RUKjK RZQhQ RXWjW RY[k[ RYSY[ RZNZQ RaSaW RZMhNhQ RXSjSiW RUHjHjK RkXk[ RUHUQTY',
	    'PoSElE RULjL RWP^P RdPlP RXS^S RdSkS RdVlV RdLd[ RUHjHjL R^L^W[ZX[ R^VVW RUHUQTY',
	    'Po`DgD RVE`E RVKjK RVHjHjL R[UYX RiNfQ RiUfW R[NYQ RaMcPeRjT RaM`P^RYT RaRcWfYl[ RaS_W]YW[ RbD]D RVEUQTY RiCbD',
	    'PoTKjK RRRlR RhD^FUG R_F_\\Y\\',
	    'PoRIaI RS[Z[ RZCZ[ RaPRR',
	    'PoSIlI RZ[`[ R`C`[ RhLfO\\TRW',
	    'PoRI]I Ra[k[ RS[W[ RXCX[ RbCbZ RlVkZ R\\PRR',
	    'PoRIZI Rh[l[ RS[W[ RWCW[ R\\JhJhZ R_W[[ RlVlZ RaR]Y RZPRR RbCbL`V',
	    'PoRI[I RS[W[ Rf[j[ RWCW[ RaV[[ RbQ_Y R[PRR R]JlJlTj[ RcCcLbU',
	    'PoRJ]J RS[X[ RXCX[ RcCc[ RcMlQ R]PRR',
	    'PoRI\\I R^SZ[ R\\PRR RWCW[S[ R_FgFgKiX R_I]V',
	    'Po]FlF RRI\\I RS[W[ R`[f[ RWCW[ RfFf[ R\\PRR',
	    'PoRI[I Rd[j[ RT[W[ RWCW[ R_R[Z R[ORR RgMlMlRj[ R`F`L^V R[FgFfO',
	    'PoRI\\I RS[W[ RWCW[ R]PRR RcD^Z RjX[[ RhTk[',
	    'PoRI\\I RS[W[ RWCW[ RgVl[ R_V[[ RePiY RbO^X R_EdEdK`U R]PRR RdHfT',
	    'PoRIZI R\\LkL RZZlZ RS[W[ RWCW[ RcDcZ RZPRR',
	    'PoRI[I Rd[k[ RS[W[ RWCW[ RdFdZ RlVk[ R[PRR RiD]G RgN\\P RiN^P RkNaO',
	    'PoRI[I R\\IlI RS[W[ RWCW[ R^MaTeXl[ RdT_YZ[ ReCeOcV RZPRR ReKdT',
	    'PoRI[I R^QhQ Rd[j[ RS[W[ RWCW[ R`C\\M R[PRR R^IlIlRj[',
	    'Po]FkF RRI[I R\\YlY RS[W[ RWCW[ RdFdY R[PRR',
	    'Po]ElE RRI]I R]NlN RS[X[ R`[d[ RXCX[ ReEe[ R\\PRR',
	    'Po^EjE RRIZI R\\LlL Rc[h[ RS[V[ RVCV[ RZORQ R`L_QjQh[',
	    'Po^EkE RRI]I R]NlN RS[X[ RXCX[ ReEe[ R\\PRR',
	    'PoRIZI R[NfN RS[V[ RWCW[ RaEa[ RlWl[ RZPRR R[EhEiSjZ',
	    'PoRI[I RS[W[ RWCW[ RaIeM R^JbTgYl[ RjIfR`XZ[ R]FjFhP R[ORR',
	    'PoRI[I RgTjT R_[k[ RS[W[ RWCW[ R_F_Z RdCdU RkHkS RlWk[ RkI\\M R[PRR',
	    'Po^HlH RRIZI R][k[ RlWk[ RdP\\Y RfNbS R]MiMeQ R_C\\L RZORQ RWCW[S[',
	    'PoRI\\I R_XlX RT[W[ RXCX[ R_F_Z R_FlFlZ R\\PRR',
	    'Po[FkF RRI[I Rg[l[ RS[W[ RWCW[ RgFg[ RlWl[ R`F`R\\Z R[PRR R`M^V',
	    'PoRI[I R\\PlP Rb[k[ RS[W[ RWCW[ RcCcZ RlWk[ R[PRR RjH\\J',
	    'PoRIZI R]NlN RS[V[ RWCW[ ReEe[ RZORR RjD^F',
	    'PoRJZJ RS[W[ RWCW[ R_VZ[ R]PeT R`Q]X RZPRR RlXk[i[ R\\JhJhVi[ RaCaN`U',
	    'PoRI[I Rc[h[ RS[W[ RWCW[ R_VZ[ R^J_NfTlW RaP]Y R[PRR R\\FjFjPjSh[ RbG`U',
	    'Po^HlH RRIZI RS[V[ RVCV[ RcDdF RZPRR R^H]Q\\Y',
	    'PoRIYI Rg[l[ RR[V[ RVCV[ R\\H\\[ RcDlDl[ R^D`H RYORQ',
	    'PoRI\\I R_OlO R^YlY RS[W[ RXCX[ R^FlFl[ R\\PRQ',
	    'PoRI[I Rf[j[ RS[W[ RWCW[ RbO\\U RbL`Q R[PRR RhLdT^Z\\[ R\\EhEeHcI]LlLlRj[',
	    'PoRI[I R\\OlO R[ZlZ R`R`Z RaPaW RaMaT RaHaQ RaEaO R]ORR RWCW[S[ R]EiEiUhY',
	    'PoRIZI Rc[h[ RWCW[ R`W\\[ RaEgEjL R_G]L RZORR RS[V[S[ RaO`W R]OiOiTh[',
	    'PoRI[I ReMlM RZZlZ RS[W[ RWCW[ R^I^Z ReCeZ R[PRR',
	    'PoRI[I R\\JlJ Re[k[ RS[W[ RWCW[ ReNeZ RfDjH R_W[[ RlWl[ RaQ]Y R[PRR RbCbK`U',
	    'PoRI[I RS[W[ RWCW[ RfLlLhTdY^[ R[ORR R\\EgEfL R_E_L\\W R_McThYl[',
	    'PoRIZI RS[V[ RWCW[ RbJdM RcDkL RcD\\L R]QiQeZ RZPRR',
	    'Po^EkE RRI[I RS[W[ RWCW[ R_LcThYl[ R^LkLgTbY][ R[ORR R^E^P\\Y',
	    'PoRIZI RS[V[ RWCW[ RgMg[ RdFkM RdCeG RbF[M RcCaI RZPRR R`M`S_X',
	    'PoRI[I RbNfR RdFkM RbG\\M R_VeX RcCaJ R[PRR RWCW[S[',
	    'Po^IjI RRIZI R\\PlP RS[V[ RVCV[ RZPRR RcCcPaU[[ RcPfUk[',
	    'PoRI[I R\\NlN RS[V[ RWCW[ RgVl[ RbT\\Z RdEdPiY RcP`W RdLfV RZORQ RcKbT RjD^F',
	    'PoRI[I R^KlK RT[W[ RWCW[ ReCeJ R^D]RhQh[ R]TY[ R[PRQ R]M\\X',
	    'PoRIZI R]MbM Re[l[ RS[V[ RWCW[ R]D]Z ReDe[ RlWl[ RjLeN RZORQ RbYZZ',
	    'PoRI[I R]NlN RS[W[ RWCW[ R]F]Z R[ORR RlWk[i[ RfEfNgY RjD]F ReYZZ',
	    'PoRI[I RfVk[ RcHcPhX R_HkHjN R`D[N RcOaU]YY[ RcMfV R[PRR RWCW[S[ RcKbT',
	    'Po]ElE RRIZI RfSjS Rb[k[ RbJbZ RaJjJjR RlWk[ RZPRR RVCV[S[ R]E]R[Y',
	    'PoRI[I R]SjS Rd[k[ RS[W[ RWCW[ R]K]V RdCd[ RjKjU RlXl[ R[PRR RjF\\H',
	    'PoRI[I ReDhH RcRfZj[ RlWk[ RhNgQaX\\Z R[PRR RWCW[S[ RaEdV RbK\\K ReJ_K RiJbK',
	    'Po[MdM RZQdQ RXVfV RY[_[ R_I_[ RVEgE_I RSKYKVTRX ReJjWlX RkKgN',
	    'Po\\HkH RRIZI RS[V[ RWCW[ RcCcN R^N`SeXl[ R\\NjNhRcWZ[ R[PRR',
	    'PoRIZI Rf[l[ RS[V[ RWCW[ RgOgZ R_W[Z R_I\\L RlWl[ RbC_J RZPRR R`O^X RdL\\M RfFiIjLbL',
	    'Po]FlF RRI\\I R^OkO R\\ZlZ RS[W[ RWCW[ RdFdZ R\\PRR',
	    'PoRI\\JlJ RS[W[ RWCW[ RcCc[ RcNkR R\\PRR',
	    'PoRI[I RiHlM R_G\\N RiPgTdX\\[ R[PRR RdCdR`R RWCW[S[',
	    'PoRI[I R_UgU R]UdU Rd[j[ ReQgV R`D\\M RbL_U R[PRR RWCW[T[ R_HlHlSj[',
	    'Po\\IlI RRIZI RS[V[ RWCW[ RdCdI R`LcUhYl[ RiJeS`XZ[ R_IbS RZPRQ',
	    'PoRI\\I R_OlO R_YlY RS[W[ RWCW[ R_E_[ R_ElEl[ R\\ORR',
	    'PoRI\\I R_KlK R_RlR Rg[l[ RT[W[ RXCX[ R_ElEl[ R\\PRR R_E_R^X',
	    'PoRI[I R\\QlQ RS[W[ RWCW[ R]HiHiQ R[ORR RcCcQaU[[ RcQeUk[',
	    'PoRIZI R]PjP R]ZkZ R]E]Z RdEdP R]EjEjR RlVkZ RZPRR RVCV[S[',
	    'PoRIZI R`[h[ RS[V[ RVCV[ R`I`[ RiMlT R_DfG R\\NZU RZORQ RiVh[',
	    'PoRIZI Re[l[ R\\I\\M ReNeZ R\\IjIjM RlWk[ R_UZ[ RbN^X RcCcL`U RZPRR RWCW[S[ RcGaS',
	    'PoRIZI R\\NlN RS[V[ RWCW[ RhCh[ RdD[G RZORQ R`F`Q_W',
	    'Po]FjF RRI[I R\\NkN ReYkY R_ZeZ R\\ZbZ RS[W[ RWCW[ RiTl[ RbN^Z R[ORR RhYcY',
	    'Po]FjF RRI[I R\\NlN Rf[l[ RS[W[ RWCW[ RgNg[ RlWl[ R_W[[ R[PRR R`N_W',
	    'PoRI[I RS[W[ Rg[l[ RWCW[ R]J][ R]JlJl[ RfPjU RcQ_U R^DdDdMaS RdJfR R[ORR RdHcP',
	    'PoRI[I RgXlX RS[W[ RWCW[ R]F]W RdGd[ RdGlGlW RdD]F RZORR RbUZW',
	    'PoRIZI RS[V[ R`[d[ RWCW[ RdMd[ R`HcIfL R\\MlMiS R^EjEgHcI RZPRR',
	    'PoRIZI RS[W[ RWCW[ RcEc[ RZORR RiDiMkZ R]F]P[Y RiD]F',
	    'Po\\FkF RRI[I RdId[ RgMlQ ReFcJ[QRR RWCW[S[',
	    'PoRI[I RgLlL RS[W[ RWCW[ R_DgDhL RlIlL R_P`TdXk[ R]OiOhScW[[ RZORR R_D^K',
	    'PoRI[I RS[W[ RWCW[ RhCh[ R^McO R_FdH R[PRR RiS\\U RkS`T',
	    'Po\\HkH RRI[I RS[W[ RWCW[ RcCcG R[PRR R_L_S^X R_LgLh[k[mW',
	    'PoRI[I R^MlM RS[W[ RWCW[ RfMf[ R[ORR R^E^Q]X RjD^F',
	    'PoRI[I R\\LkL R[RlR R_[c[ RdFd[ R[PRR RWCW[S[ RjD]F',
	    'Po]EjE RRI[I R[MlM RdZlZ RS[V[ RWCW[ ReMeZ RlWlZ R_VZ[ RaP^X R[ORR RcEbJaT',
	    'PoRIXI Rb[k[ RS[U[ RUCU[ RZJbJbZ RlWk[ RYORQ RjWgW RdIkIkRjW R]C]O[X RgCgLeV',
	    'Po\\HlH RRI[I RS[W[ RWCW[ R[ORR RaC_N_R[Z R`NjNgUcY_[ R`NcTgYk[',
	    'PoRIYI RR[V[ RVCV[ R`D[O R^S\\Y RcK]W RYPRR RbN[O RbUcYZY Rj[f[ RcElElSj[',
	    'PoRI[I RS[W[ RWCW[ R\\PRR R_EjEjO_O R_E_O]T[[ RdOgVl[',
	    'Po\\GkG RRIZI R[LlL RS[V[ RVCV[ RbWf[ R_VcW R^QiQcX RbC^Q RZPRR',
	    'Po]EkE RRI[I R]ZlZ RS[W[ RWCW[ R]E]Z RdR_W R`IdMfOgRiV RhHdS R[PRR',
	    'PoRIZI R_[j[ RS[V[ RWCW[ R`M`Z RcDkL RkWjZ RcCbG[M RhO_T RZPRR',
	    'PoRI[I RbUgU R_[j[ RS[W[ RWCW[ R`M`Z RjWj[ RdFlM RcCbG[M R[PRR R_MhMgT',
	    'PoRI[I RS[W[ Re[j[ RWCW[ R`LgP RaC]M RZPRR RhR]V R_IlHlSj[',
	    'PoRI[I R_OlO RS[W[ RWCW[ R_HlHlQ RcDfG R[PRR R_H_R^X',
	    'PoRI[I RcJiJ RR[W[ RWCW[ R^D^[ RcSjZ RhSaZ R`McT R^NkMgV R[PRR R^DjDiJ',
	    'Po\\ElE RRI[I RS[W[ Rg[l[ RWCW[ R]K][ R]KlKl[ RdEdOjT RcQ_U R[PRR RdHcQ',
	    'PoRI[I R]RjR RS[W[ RWCW[ R]F]R R]FjFjR RgUkY R`U[Z R[PRR',
	    'Po]EkE RRI[I R[QlQ RS[W[ RWCW[ RdEd[ RiIkN R`H]N R[PRR',
	    'PoRIZI R]NjN R[TlT Rf[j[ RS[V[ RWCW[ R]H][ RcCcS R]HjHj[ RZPRR',
	    'Po[GlG RRIZI RhWkW RS[V[ RWCW[ R_M_W ReJe[ R^NkNkW RcD_MZPRQ',
	    'PoRIZI RS[W[ RWCW[ RdCdN R_NaTeXl[ R\\NjNgTcX\\[ RZPRR R\\HkHjL R\\H\\R[Y',
	    'PoRI[I R^ZjZ RS[W[ RWCW[ R^Q^[ R^QjQj[ R\\McMjLkO RgHjL RbC^M R[ORR',
	    'PoRIYI RgZlZ RS[V[ RWCW[ R\\G\\K R_LgLgZ R\\GkGkK RbCcG RlVlZ RZPRR R_L_R^W',
	    'PoRIZI RS[V[ RWCW[ RdDlL RbF[M RbC`H RdK`O\\P RfOdR]U RZPRR RjSfWbY[[',
	    'PoRI[I R^LiL R^SiS RYZlZ RS[W[ RWCW[ R^D^Z R^DiDiZ R[PRR',
	    'PoRIYI RZPlP R]H]P R]HjHjP ReTkZ RbU\\Z RYPRR RVCV[S[ RcCcObU',
	    'PoRIZI R]QeQ ReTiT R][k[ RS[W[ RWCW[ R]Q][ R]KeKeQ RlWk[ R`C\\L RZPRR R^HkGkOjT',
	    'PoRI[I R]TdT Rc[i[ RS[W[ RWCW[ R]L]V R]LdLdT R[FkFj[ R[PRR',
	    'PoRI[I R]KkK Ra[k[ RS[W[ RWCW[ RbNb[ R]EkEkK RlWk[ RjOaT R[PRR R]E]Q\\Y',
	    'PoRIZI R[KlK RcTiT R^ZlZ RS[V[ RWCW[ R^D^Z RcCcT RiDiT RZPRR',
	    'PoRI[I R]MkM R[ZfZ RS[W[ RWCW[ R]F]V R[ORR ReEfKeNgX RiD_F RdUZV RlVk[iZ',
	    'Po^IjI RRIZI R[PkP RS[V[ RWCW[ ReUj[ RcCcQgX R_F[M RcPaV]YZ[ RZORQ RcLbU',
	    'Po\\FlF RRI[I R[ZlZ RS[W[ RWCW[ RdIdX RfKkQ RdI\\Q ReFcK R[ORQ',
	    'PoRI[I R^LjL Rc[l[ RS[W[ RWCW[ RcHdL R_W\\Z RiN_WaYe[ R[PRR RkD^F',
	    'Po\\HlH RRI[I R]OkO RS[W[ RWCW[ RdCd[ RfSlX RaR[Y ReOgU RbO`T R[ORR',
	    'Po]HkH RRI[I R\\OkO RS[W[ RWCW[ RdCd[ RfSlX RaR[Y RdMfQhU RbO`T RdMaR R[PRR',
	    'PoRI[I R^NlN R^TlT RS[W[ RWCW[ R^H^U ReCe[ R^HlHlU R[PRR',
	    'PoRI[I R]KlK R]RlR R]E]S ReEe[ R]ElElS R[PRR RWCW[S[',
	    'PoRI[I R]QlQ R]YlY RS[W[ RWCW[ R]J][ ReCeY R]JlJl[ R[PRR',
	    'Po]IjI RRIZI R[PlP RbZkZ R^ZgZ R\\ZdZ RS[V[ RWCW[ RcCcP RjWl[ RgTjX RaP^Z RZORQ',
	    'PoRI[I R]KjK R]QlQ R]E]Z R]EjEkK R[PRR RWCW[S[ RlWk[i[ ReY[Z ReMfX',
	    'PoRI[I R]OjO Rf[l[ RS[W[ RWCW[ R]E]O RgOg[ R]EjEjO RlWl[ R_W[[ R[PRR RaO_W',
	    'PoRIYI RfYlY R_[b[ RS[V[ RVCV[ RfGf[ RfGlGl[ R^TZ[ RYORQ R_C_N]X R\\IcIcVb[',
	    'PoRI[I R\\MkM RS[W[ RWCW[ RfCf[ R\\GkGkM R_W[[ RaCaR]Y RkXhX RZORQ R\\M[SlRkX RaL_W',
	    'Po_HlH RRI[I RcMkM RcSlS RS[W[ RWCW[ RcHc[ R`C\\L R[PRR',
	    'Po]IkI RRI\\I R^QkQ R\\ZlZ RS[W[ RWCW[ RdCdZ R\\PRR',
	    'PoRI[I R^MjM R^UjU R[ZlZ RS[W[ RWCW[ R^F^U R^FjFjU R[PRR',
	    'PoRI[I R^MlM RS[W[ RWCW[ RfMf[ RbQlV R[PRR R^F^Q]Y RjD^F',
	    'PoRIZI R[NlN R]WlW Rc[h[ RS[W[ RWCW[ RbQeT RbHeK RZPRR R^EjEjSh[ R^E^K^N]P]R\\W',
	    'PodIlI RRI[I R^ZkZ RT[W[ RWCW[ R^P^[ RdCdP R^PkPk[ R[PRR',
	    'Po]IlI RRI[I R[ZlZ RdDdI R[PRR RWCW[S[ R_LaX RhLeZ',
	    'PoRIYI RaJlJ Re[i[ R_G_[ RiCi[ RbNeT R`C\\N RYPRR RVCV[S[',
	    'PoZIaI RRIXI R`ZkZ RR[U[ RUCU[ R`L`Z RcIjIjU RlWlZ R\\RYZ RXPRR RiVfV R]C]O[V RfCfJdT',
	    'PoRI[I R]MkM R\\SlS RdCd[ RhFkI R_G\\J R[PRR RWCW[S[',
	    'PoRI[I R^QlQ R^ZlZ RS[W[ RWCW[ R^H^[ R^HlHl[ ReCcH R[PRR',
	    'PoRI[I R_LhL RfYjY RS[W[ RWCW[ RbQb[ R\\QjQjX RdDlL RbE[L R[PRR',
	    'PoTSjS RRVlV RY[_[ RUQ_P_[ RbFcIgLlO RVFUJ]M RXCVH RRF^F\\JZLSO R`EjEiHfK`N RgO_P',
	    'PoRI[I R_LlL RS[W[ RWCW[ R_D_L R_DlDlL R[ORR R_PmPk[f[ RcPbW_Y\\[',
	    'PoRIlI R_QiQ R_YiY R_D_[ RiDi[ R[PRR RWCW[S[',
	    'Po^ElE RRI[I R^SjS R^YlY R^E^[ R^KjKjS R[PRR RWCW[S[',
	    'Po\\ElE RRI[I R_ZkZ RS[W[ RWCW[ R`M`[ R_OkOk[ R_OZT RcE]Q R[PRR',
	    'PoRI[I R\\JlJ RS[W[ RWCW[ RfU`[ R`CaNdUhYl[ ReDjF RhNeW R_R[Z R`JcT R[PRR R`I^V',
	    'PoRI[I R`[k[ RS[W[ RWCW[ R]H]K R`K`[ RdCdG R]HlHlK RlWk[ R[PRR RiO`R',
	    'Po^HlH RRIZI RgVjV R_[k[ RS[V[ RWCW[ R_L_Z RdIdW RjMjU RlXk[ R_C\\L RZPRR RjM[Q',
	    'PoRIYI RS[V[ RVCV[ RZL_P RdW`[ ReSbZ R_D[L RaK[W RZORQ Rj[g[ RaRcVYW RbIlIlTk[ RfCfNeV',
	    'PoRIZI R^UfU Rd[j[ R^M^W R^MfMfU R`C]K RZPRR RWCW[S[ R^HlHlRj[',
	    'PoRI[I R^NlN R]YlY RS[W[ RWCW[ R]R][ R^F^N ReCeY RlFlN RlRl[ R[PRR',
	    'PoRIZI R[JjJ R[QlQ RS[W[ RWCW[ RgLg[ RgFlK R^W[[ RaC^J R`L`S^Y RZPRR',
	    'PoRI[I RfNjN R^ZlZ RS[W[ RWCW[ R^Q^[ R^QlQl[ R[PRR R]ElEkN RdEaJ]N',
	    'Po^ElE R_JkJ RSK]K R_OkO R]TlT ReEe[ R\\DRF RXEXQWW R]QRS',
	    'Po]EkE RRI[I R]JjJ R]OjO R[TlT RS[W[ RWCW[ RdEd[ R[PRR',
	    'PoRI[I R^MkM RZZlZ RS[W[ RWCW[ R^F^Z RfMfZ R[PRR RiD^F',
	    'PoRI[I RT[W[ RWCW[ R^E^V RgUkY RbHdM RcS[W RgTcX][ R[PRR RjDjJhT',
	    'Po\\HaH RRIYI RiVkV RS[V[ RVCV[ RdEiEiV RkSkV RYORQ R\\DZQ RdEdPcV R[J^Vc[m[ RaH^SY[',
	    'Po]HlH RRIZI RbYiY R^YfY RS[W[ RWCW[ RdCdH RhRl[ RdK`Y RZPRR R]H\\R[Y',
	    'PoRJZJ R[JlJ Re[l[ RS[V[ RWCW[ ReJe[ RlXl[ RfEiF R]VY[ RjNhR`Y R_Q\\Y R[PRR R`C`K^U',
	    'Po[GlG RRIZI R\\QkQ R`[d[ RS[V[ RWCW[ R\\KdKd[ RiTkY R_T]X RaC]Q RZPRQ',
	    'PobGjG RRI[I R\\NlN RS[W[ RWCW[ RbCb[ RgSjV RZPRR',
	    'PoRI[I R]LlL R]RlR Rh[l[ RS[W[ RWCW[ ReEe[ R]ElEl[ RZPRR R]E]R\\Y',
	    'PoRJZJ R]JkJ R^QiQ R[YlY R_DaH RhDeJ R[PRR RWCW[S[',
	    'PoRIZI R]NlN RS[W[ Ra[d[ RWCW[ R]H]L ReNe[ R]HlHlL RdCeF RZPRR',
	    'PoRIZI R\\JlJ RgEjG R^SYX R`ObShYl[ R^E\\J RbJ]U R`NiNgTbX][ RcD_R RZPRR RVCV[S[',
	    'PoRIZI R]RiR R[VlV RS[V[ RWCW[ RdOd[ R^EcJkN R\\EjEeJ\\N RZORQ',
	    'Po\\FlF RRI[I RfRkR R]YkY RS[W[ RWCW[ R]K][ RfFfR R]KkKl[ R[ORR RbFbNaR',
	    'PoRIZI R]JiJ R]OiO RS[V[ RWCW[ R]D][ R]DiDiO RbOdUhYlZ RjRdU RZPRR RdY\\Z',
	    'PoRIZI R\\LlL R]ZjZ RS[V[ RWCW[ R]R][ RdEdR R]RjRj[ R[PRR RjD]F',
	    'PoRIZI R\\IlI R\\OdO RR[V[ RVCV[ R`O`X RiDlG RZPRR ReW[X RgCgOiZk[mW',
	    'Po\\HlH RRIZI R]NkN R^ZjZ RS[V[ RWCW[ R^R^[ RdCdN R^RjRk[ RZPRR',
	    'Po]EjE RRIZI R`WcW R[[k[ RS[W[ RWCW[ RcIcW RfPlV R_QZU RkJgN RcJgR R[LaL]T RiFcI RZPRR',
	    'PoRIZI R`OlO RaZlZ RS[V[ RVCV[ R_H_[ RgFgZ R^IZM R`C]K RYPRR RkEaG',
	    'PoRIZI R\\JlJ R[SlS RS[V[ RWCW[ R`D`R RhDhR RgVl[ R`V[[ RZPRR',
	    'PoTGjG RRKlK RWSgS RTVjV R[[_[ R_O_[ RdCdLhPlR RZC[LVQRR RcN[O',
	    'PoTHjH RRLlL RWSgS RTVjV RZ[_[ RYP_P_[ RWDYG RfDdH RbHcKhPlQ R^C[KVPRQ RcO[P',
	    'PoRIZI R]NiN R]TiT R[ZlZ RS[W[ RWCW[ RcNcZ R]EdEkL RaF[M RZPRR',
	    'Po[HlH RRIZI R`RlR RS[V[ RWCW[ R^L^[ R^MZQ RaE]O RZNRQ RbLkLfPf[c[',
	    'PoRI[I RaQjQ R\\H_L RbHfL RhHlM RkDhG RcN]T RjRdX R_E\\G ReDbG R_ScTfW][ R[PRR RWCW[S[',
	    'Po\\GgG RRIZI R[LlL Rc[h[ RS[V[ RWCW[ RbCbK RjFbNZR R_TjTh[ R`O_U RZORQ RiO`P',
	    'PobGjG RRIZI RS[V[ RWCW[ RdC^I RbIeL]P RiHeMaR]T RcTgW_[ RZPRR RcPlPjSeX\\[',
	    'PoRIYI ReLiL R]Z`Z RS[V[ RVCV[ RcCaI_K[L RYPRR R\\FjFiL RkZgZ R^N\\X RhNfX RdQlQkZ RZQbQ`Z',
	    'Po[FlF RRIZI RS[V[ RVCV[ R\\K\\R RcCc[ RdRkX RcOaS\\X RcOeS RZPRR R\\KkKjRgR',
	    'Po[GlG RRIZI RS[V[ RVCV[ RfVj[ ReJeShY RkLhR R_K[T RdVaY][ R`F]Q R`M`R RZPRR RaC^O ReMdW',
	    'PoRIZI R]IkI R\\QlQ RS[V[ RWCW[ RgHg[ R_W[[ R_DaH RiDfI RZPRR RaIaR`W',
	    'PoRIYI R\\LjL R\\QjQ R\\G\\R R\\GjGjQ RgZj[ RiSeW\\[ RlWk[ RYPRR RVCV[S[ RcCcLeW',
	    'PoRI[I R_LiL R^ZjZ RS[W[ RWCW[ R^Q^[ R^QjQj[ RcClL RcD[L R[PRR',
	    'PoYHeH RUQbQ RTTjT RRWlW RZ[_[ R_Q_[ RWKgKgN R_CeGlH R_DWGRI RdQ_Q RWKWNgObQ',
	    'PoRI[I RS[W[ RWCW[ RaKaZ RkNgRjZ RfY`Z ReLiX RZPRR ReJhV RkIaK RjD]F]Q\\Y',
	    'Po]GkG RRIZI R\\MlM R\\QlQ Rc[h[ RS[V[ RWCW[ RdCdL RhMh[ R`TaX RZPRR',
	    'Po]HjH RRIZI R[NlN R\\TkT RZZlZ RR[V[ RVCV[ RcCcN RcPcZ RZPRR',
	    'Po\\ElE RRI[I R]MkM R]SkS R[ZlZ RdOdZ RfIlN RbE^M R[PRR RWCW[S[',
	    'PoRIZI R[NlN Rf[l[ RS[V[ RWCW[ RdCdN RfNf[ RlWl[ R_W[[ R]E_K RjEhK RZORQ R`N_X',
	    'PoRIZI RgNjN R\\RkR RS[V[ RWCW[ RcNc[ ReSkY RcQgU R^M[N RbR]XZY R\\DfDfIkIjN RZPRR R`D_L',
	    'PoRIZI RgLlL R\\QkQ RS[V[ RWCW[ RcMc[ R`DgDgK RcQjX RcQ\\X R`I\\M R`E_K RZPRR',
	    'PoRI[I R^MkM R^UkU R^ZkZ RS[W[ RWCW[ R_C_L R^P^[ R^PkPk[ RlIkM R[ORR RjF^H',
	    'PofMjM RTSjS RRVlV RZ[_[ RYCYM RVQ_P_[ R_EkEkL RcJ_M RdEbK R\\ESF R]HSJ R]KRM RbP]P RgO_P',
	    'PoRI[I R\\NlN RS[W[ RWCW[ R\\G\\K RdCdG R\\GlGlK R`UlZ R^UgW RbJ^U R[PRR RiPfUdX`Z[[',
	    'PocGjG RRI[I R\\LlL R[PlP RS[W[ RWCW[ RcCcL RcPc[ RcSkW R[ORR',
	    'Po]EkE RRI[I R]RjR R]ZlZ RS[W[ RWCW[ R]E][ RdEdK RdRdZ R]KjKjR R[PRR',
	    'PoRI[I R^ZiZ RS[W[ RWCW[ R^R^[ R^RiRj[ R`F[K R`GaJfOlQ RaC_H R`GiFgJbO[R R[PRR',
	    'Po\\HkH RRI[I RS[W[ RWCW[ RdCdG RgJlN R`K\\O R`ObUfYl[ RgOeTaX[[ R[PRR',
	    'Po[GlG RRIZI R_MhM R\\QlQ RS[V[ Rb[h[ RWCW[ RdGjM RbG\\M RcC`J RaQ_UiVh[ RZQRR',
	    'PoRI[I RaIiI RbUhU Rh[l[ R^E^[ RbMbV RbMhMhU R^ElEl[ R[PRR RWCW[S[',
	    'PobMkM RTSjS RRVlV RZ[_[ RVQ_P_[ RbEbM RbEkEkM RWEUJ]M RXCVH RRF^F]JZLTO RgO_P',
	    'PoRIZI Rf[l[ RS[V[ RWCW[ RfCf[ RlWl[ RfOkT R\\G^L RjFhK R`Q[S RZPRR RaCaQ_V[Z',
	    'Po[EeE RRIYI RR[V[ Rg[l[ RVCV[ RfFfU RlDl[ R^PaS R`U[[ R[O`Q R]IZR RcN^X RYORQ R^E[P R]KcK`V',
	    'PoRI[I Rc[l[ R\\G\\K RcLc[ RdCdG R\\GkGlK RlWl[ R[PRR RWCW[S[ RiK]M RbS[S ReR^S RgR`S RkRcR',
	    'Po[IlI RRIYI RS[V[ RVCV[ R`N`[ RgUlZ RbK[R RkMgQ RdNhW RcDdG RYPRR RcJfT ReY^Z',
	    'Po\\ElE RRI[I R_KiK R_PiP R[WlW R_E_W RiEi[ R[PRR RWCW[S[',
	    'PoRIZI RfOlO R][l[ RS[V[ RWCW[ R\\G\\J RcCcG RfIfN R\\GkGkJ RlXk[ RbU]Y R]QhQdS RaI_M\\N RZORR',
	    'Po[GkG RRIYI R\\OjO RhWjW RS[V[ RVCV[ R\\JjJjN RcCcReWgYl[ RaU^YZ[ R\\N\\RkRjW RYPRR RcMbU',
	    'PoRI[I RaLdL Rd[i[ RS[W[ RWCW[ RdCdL RjFlK R_F]J RcU_Y[[ RdObW R[PRR RhJeN`P[Q R]RkSi[',
	    'PoRIlI RVQhQ RSUkU RY[_[ R_M_[ RVDYG R]C_G RhDcIhNlO R[IVNRP RcLZM',
	    'PoSF\\F RTTjT RRWlW RZ[_[ RXCXO RTPVQ`Q_[ R^FiFiN RlLlO RbK^O R_JeL RcDaL R\\JRK RfP`Q',
	    'PoRFlF R_NcN RTSjS RRVlV RZ[_[ R_C_F RUP`P_[ RcFcM RfIiJlL R]F[KZMUN RWIRK RgO`O',
	    'Po[EkE RRIYI RZZlZ RS[V[ RVCV[ RaEaZ RfEfZ R\\K^T RjKiT RYPRR',
	    'PoaHlH RRIYI RdVhV RbZlZ RhDhV RZN^N^VaY RcMeQ R^VZZ R\\E^I RXORQ RVCV[S[',
	    'PoRIYI R`JlJ Rb[l[ R\\E]I R[M^M^X[[ RfCfLkV ReObW RYPRR RVCV[S[ RfHdR',
	    'Po\\HkH RRIZI R[QlQ RS[V[ RVCV[ RfUk[ R^J_O RdCdQgW RiJhO RbU^YZ[ RZPRR RdLbU',
	    'PoeGkG RRIZI R[RlR Rf[l[ RS[V[ RVCV[ RfRf[ RkLkO RlXl[ R`W\\[ R]HbGcKfNiO RZORR R`CbK RaR_Y RiJcM\\N RhFaG',
	    'PoRI[I R]SlS R\\YlY ReCeM R]MlMl[ R^E`K RkEjK R[ORR RWCW[T[',
	    'PoRIZI R[KlK RR[V[ RVCV[ RgQg[ ReKkQ RaL[Q R\\FcF_N R_T]Z RZORQ R_Q_X RiD`E',
	    'Po_FgF RRIZI R[OlO R\\TjT R_[c[ RR[V[ RVCV[ RcJc[ R\\KjJjU RfGcK R_F[K R`C^I RZPRR',
	    'Po[GlG RRIYI RgQg[ R_W[[ RbCcG R_IcMiP RYPRR RhGgJcNlP RdN[P RVCV[S[ R_Q_X',
	    'PoRIYI R\\JjJ R]RjR R[VlV RS[V[ RVCV[ R[E[I RdMd[ RaH]Q RYQRR R[EkEkI',
	    'PoRIZI R^IjI R]NjN R[RlR Rc[h[ RS[V[ RWCW[ RhPhZ R]EjEjN R^TbX RZPRR',
	    'PoRIZI R^[b[ Rh[l[ RS[W[ RWCW[ R[EbEb[ ReElEl[ R_PZU RiHeM RiPdU R_H[M RZORQ',
	    'PoRI[I R\\JkJ R^OkO R[TlT RS[W[ RWCW[ RgFlK RbC_I R`K]R R[PRR RdOdSaW[[ RdSgWl[',
	    'PoRI[I R]RiR Re[l[ R]K]Q RfRf[ R]KiKiQ RlXl[ RhGlK R_W\\[ R^HZK R`EgDjJ RaR_Y R[PRR RWCW[S[',
	    'PoRIZI R[KdK RZRdR R^[a[ RS[V[ RWCW[ RfEf[ RZORQ RfElEiMlRlVhW R^E^O\\Y R[EbEbWa[',
	    'PoRIZI R\\SjS RZZlZ RS[V[ RVCV[ RcCcZ R]JaN RgJlO R^IZP RhHeO RYNRR R^F\\O',
	    'Po]FlF RRIZI R_OiO R\\TlT RS[V[ R`[d[ RWCW[ R]K]N RdCdK RdRd[ R]KlKlN RhPdR RZORR',
	    'Po\\ElE RRI[I R]MkM R]RkR RS[W[ RWCW[ R]I]R R]IkIkR RbU_Y[[ R^TaXeZk[ R[PRR RcEcQcV',
	    'PoRIZI R]MkM RdHd[ R[HkHkM R^C`G RiDgF RcQ]XYY RZPRR RVCV[S[ R\\M\\RlRkXgX',
	    'Po]EkE RRIZI R_JjJ R]NlN RS[V[ RWCW[ R`N`Z RjQgT RZORQ RfY`Z R]E\\Q[Y ReNgVk[',
	    'PoRIZI R]KjK R\\OkO R\\TkT RZZlZ RS[V[ RWCW[ R]D]K RcOcZ R]DjDjK RZPRR',
	    'PoRIYI RS[V[ RVCV[ RfCfS R\\D`E RiHlN RZK^M R^RZZ RYPRR RjQhUeY_[ RbH`P',
	    'Po_JcJ RTSjS RRWlW RZ[_[ RVQ_P_[ RcCcJ RhElI RWJRO R^EZI RRGVH RTDXD RgJbLZO RhO`P',
	    'Po[ElE RRI[I R\\SkS RY[l[ RS[W[ RWCW[ RcScZ R[L_O RaLeP RfLkP RjHfK R^H[K RdHaK R[PRR',
	    'Po^GkG RRIZI R[QlQ R\\XlX Rd[h[ RS[V[ RWCW[ RaSeU RbMfO R_C\\K RZPRR R^KjKjUi[ R^K\\X',
	    'Po]ElE RRI[I R]IkI R\\NlN R[TlT RS[W[ RWCW[ RdEdN RhPh[ R`P`W\\[ R[ORR',
	    'PoRI[I R\\JkJ R`WgW RS[W[ Rf[j[ RWCW[ R`R`X R`RgRgW R\\DkDkJ RZPRQ R\\NlNlUk[ R\\D\\R[Z',
	    'Po[GcG RRIYI R[McM RZScS RS[V[ RVCV[ ReEe[ RjNlT ReFlEiN RYPRR RlWhW R_C_R^X',
	    'PoRI[I R]IjI R]NjN R]SjS Rf[l[ RS[W[ RWCW[ R]E]S RfSf[ R]EjEjS RlWk[ RaS^X]ZY[ R[PRQ',
	    'PoRIZI R^JiJ R]SkS R][k[ RS[V[ RWCW[ R]M]Z R^D^J RdMdS R^DjDjJ R]MkMkS RlWkZ RZPRR',
	    'PoRIYI RbMlM RbVkV Ra[l[ RgEgV R]WY[ R_R\\Y RYORQ RZE_E[N`N]W RVCV[S[ RkEbF R[R]W`Zg[',
	    'PoRIYI RgMlM RcVlV Ra[l[ RcJcV RhEhV R]WY[ R_Q\\Y RYORQ RZE_E[N`N]W RVCV[S[ RkDbF R[R]W`Zg[',
	    'Po^EeE RRIZI R\\PlP RS[W[ RWCW[ RcGdK RiFlL R_Uk[ R^E\\L RbM^V RZPRR RiPgUdXaY[[ RiDaE',
	    'PoRIYI R\\RjR Rd[l[ RS[V[ RVCV[ R]J]Q ReReZ R[KjKjQ RlWl[ R_GZK R`C^H RaU^YY[ RYPRR R_FfFcKbU',
	    'Po[HkH RRIZI RS[V[ RWCW[ R^NaQ ReUjZ RhNlR RcCcQfX RiKeR R^L[S RcPaV^YZ[ RZORQ RcMdU',
	    'Po\\IlI RRIZI R]QkQ R]UkU RS[W[ RWCW[ R]L]W R\\EdEd[ R]LkLkV RZPRR RjDbE',
	    'PoRIZI R]PjP R^SjS R\\WlW RS[V[ RWCW[ RdNd[ RbD\\J R`GdKlN R`FiFeJ]N RZPRR',
	    'Po^HkH RRI[I R\\MlM R^ZkZ RS[W[ RWCW[ R^R^[ RdCdQ R^RkRk[ R_D\\K R[PRR',
	    'Po]EkE RRI[I R\\OlO R^ZjZ RS[W[ RWCW[ R^S^[ R^IiIiO R^SjSj[ R[PRR RbE`O',
	    'PoRIYI R\\IlI R\\NjN R_ZjZ R_Q_[ R\\EjEjN R^SjSk[ R_QZV RaK]T RbF_Q RYPRR RVCV[S[',
	    'Po\\HkH RRIZI RS[W[ R^[c[ RWCW[ RcCc[ R\\L_N RfSlY RjMgP RcLgV RgDkF R_UZW R[PRR RcHeR',
	    'Po]ElE RRI[I R]PlP R]UlU Ri[l[ R]K][ RdKd[ R]KlKl[ R`GiK RkFdH RZPRR RWCW[S[',
	    'PoRI[I R_KjK R]YlY RS[W[ RWCW[ R]D][ ReGeW R]DlDl[ RgQkT ReJiS RdJ`T RZORQ',
	    'Po\\GjG RRIZI R[LlL RS[V[ Rb[f[ RWCW[ RcCcL RfLf[ RiPlV R]PZV R]Z[[ RZORQ RaN_X',
	    'PoRIZI R^KjK R\\QlQ R`[d[ RS[V[ RWCW[ RdKd[ RiTkY RdDgHlJ R^S\\X RdC`H\\J RZPRR',
	    'PoRIZI R^LjL RdRkR RVCV[ R^E^L R^EjEjL R^Q\\Z RZPRR R]T_Xc[l[ RdLdZ RV[S[',
	    'PoRI[I R^MjM R[SlS R`[d[ RdQd[ RcGdK RjGlK R_E\\L RiNdP R[PRR RWCW[S[ RiD^E',
	    'PoRIZI R[PlP RS[V[ Rc[h[ RWCW[ RcGcK RhMh[ R^SbW RiFkL R^E[L RZORQ ReD^E RiD`E',
	    'PoRIZI R\\LcL RS[W[ Rh[l[ RWCW[ R\\D\\L R\\DcDcL RgFgU RlCl[ Rb[_[ RZORQ R^L]X R^QcQcVb[',
	    'Po^IkI RRI[I R^MkM R^PkP R\\UlU RS[W[ RWCW[ R^E^M RdPd[ R^EkEkM R[PRR',
	    'PoRI[I R^PjP R^UjU RS[W[ Rf[j[ RWCW[ R^K^[ RdCdK R^KjKj[ RhElI R_F\\J R[ORR',
	    'PoRI[I R^IjI R^NjN R]SkS R[ZlZ RS[W[ RWCW[ R^E^N RdPdZ R^EjEkN R[PRR',
	    'PoRI\\I R_JkJ R^QlQ R^UlU Rh[l[ RS[W[ RWCW[ R^M^[ R_D_J R_DkDkJ R^MlMl[ R\\PRR',
	    'PoaIjI RRIZI RaMjM R]PeP R[TlT RS[W[ RWCWZ R]F]O RaDaM RaDjDjM RcThYl[ ReQcV`Y[[ RZORQ',
	    'Po[GkG RRI[I R\\QjQ RS[W[ RWCW[ R\\K\\Q RcCc[ R\\KjKjQ ReSlZ RbS[Z R[PRR',
	    'PoRIZI R[NdN R_[b[ R]E]N R]EcEb[ RfEf[ R^TZY RbM]V RZORQ RWCW[S[ RfElEiMlSlWhW',
	    'PoaFhF RRI[I R^OlO R^TlT RS[W[ Rh[l[ RWCW[ RfJfT R]KlJl[ RgGeL R[ORR RaF]J^T]Y',
	    'Po\\HlH RRI[I R]QkQ R]UkU Rg[k[ RS[W[ RWCW[ R]L][ RdCd[ R]LkLk[ RgDkF R[ORR',
	    'PoRIZI R_LiL R\\QlQ Rf[l[ R\\G\\J RgQg[ R\\GkGkJ RlWl[ RbDdF R`W\\Z RaQ_X RZPRR RWCW[S[',
	    'PodGjG RRIZI R[LlL R`UdU RS[V[ RWCW[ R^F^L RdCdU R^P[T RjOhTdX][ RZORQ',
	    'PoRI[I R\\IkI RS[W[ RWCW[ RfIfOkMkO RgFlJ RbP\\V R_SdXk[ RaC_I RaI_N[ORQ R`RiSeWaYZ[',
	    'Po\\GlG RRI[I R]NkN R]SkS R[WlW RS[W[ RWCW[ R]J]S RdCd[ R]JkJkS R[PRR',
	    'PoRI[I R]JkJ R]PkP R\\TkT R[ZlZ RS[W[ RWCW[ R]E]P RdEdZ R]EkEkP R[PRR',
	    'PoRI[I R_LkL R^ZlZ RS[W[ RWCW[ R^Q^[ R_D_L R_DkDkL R^QlQl[ ReLcQ R[ORR',
	    'PoRI[I R]JjJ R]NjN RS[W[ RWCW[ R]F]N RcCcR R]FjFjN R^SbXfZl[ R[ORR R\\RjRfWbYZ[',
	    'PoRI[I R^QjQ Rf[l[ R^I^Q RgQg[ R^IjIjQ RlXl[ R`W\\[ R_CaG RiCgI RaQ`X R[PRR RWCW[S[',
	    'Po[FlF RRIYI Rd[h[ RR[V[ RVCV[ R\\L\\O R`C`I RgCgI R\\LlLlO RbV]Z RcOaW RYPRR R]RjRi[',
	    'PoRIZI R^JiJ RS[V[ RWCW[ R]M]V R^D^J R^DiDiJ R]MjMjV ReXl[ RcU`Y[[ RZPRR RcPcV',
	    'Po]EdE RRIZI R]KkK R[TlT RS[V[ RVCV[ R]E]S RfKfS RgVl[ R_V[[ RZPRR RiDaE',
	    'PoRIZI R_LgL R[ZlZ RS[V[ RVCV[ RcDlM RcCbG[M RjOeZ R]P^V RZPRR RcOcU',
	    'PoRIYI RZRlR RS[V[ RVCV[ R]I]R R\\JiJiR RcJcRdUjZ R_FhFcK R`E[J R`C^H RcPaU^YY[ RYPRR',
	    'PoRI[I R[XfX Re[j[ R[S[X RaRaX RgTgY RcCbF RaIeK RiMeM RZPRR RWCW[S[ R^F^PlPk[ R^FjFiM',
	    'PoRIZI R\\JkJ Rd[l[ RdMd[ R\\EkEkI RlXl[ RZPRR RiL^M RWCW[S[ R\\E\\P[X RjP^R RkT]V',
	    'PoRIZI RhUjU Re[l[ RS[V[ RWCW[ R[G[J ReLe[ RdLjLkT R[GkGkJ R_V[[ RlXl[ R[QaR^Y RcCcG R]J[R R]NbM_W RZORQ',
	    'PoRIZI R^LhL R\\QjQ R[ZkZ RS[V[ RWCW[ RcLcZ RcCjK RcC[K R^S_X RiSfX RZORQ',
	    'Po\\FkF RRIZI R\\JjJ R[MlM R^RhR R\\VjV RS[V[ RWCW[ RcPc[ RfMlS R`MZS ReJhP RbH^P RcC`N RZPRQ',
	    'PoRIZI R^KiK R[PkP R^ZiZ RS[W[ RWCW[ R^S^[ RcHcP R^SiSi[ R^IZJ RcC\\J RcChIlJ RZORR',
	    'Po\\ElE RRIZI R]NkN R^SlS RS[V[ RWCW[ R]HkHkN RfVjZ RdRgY RcVaY][ RZORQ R]H]R[X RdNcW',
	    'Po]IlI RRIZI R]VjV R]ZjZ RS[V[ RWCW[ R]E]O R]Q][ R]QjQj[ RlMkP RgNkP R]EdEfM RZPRR RbN\\N RjCbD',
	    'Po\\IbI RfIlI RRIZI R\\MbM RfMlM Rg[l[ RS[W[ RWCW[ R\\D\\[ R\\DbDbM RfDfM RfDlDl[ RZORQ',
	    'Po\\GkG RRIZI RbMlM RS[V[ RWCW[ R`K`[ RdCdG RdQfU RaI]R RYPRQ RiIi[e[ R\\G\\RZ[',
	    'PoRI[I R]KjK R]PjP RZUlU RS[W[ RWCW[ R]F]P RfQf[ R]FjFjP RcFcPaR]T RbCbG R[PRR',
	    'PoRIZI R]OlO R_ZkZ RS[V[ RWCW[ R_S_[ ReJeS R_SkSk[ RZPRR R]EkEkJ]J R]E]J]Q[Y',
	    'Po[EeE RRIYI R[ReR Rg[l[ RS[V[ RVCV[ R`N`X RgEgU RlDlZ R_E[M RYORQ ReXZY RbHdL[M',
	    'PoRIZI R\\IkI R\\NkN R[QlQ RS[V[ RWCW[ R\\D\\N RcDc[ R\\DkDkN RcQkY R]WZY RcQ\\X RZORQ',
	    'Po\\DkD RRIZI R_JkJ R^OlO R^TkT R\\ZlZ RS[W[ RWCW[ RdFdO RdPdZ RZPRR R\\D\\R[Z',
	    'PoRIZI R^JiJ R[NlN RS[V[ R_[j[ RWCW[ R_R_[ RgOlS RgEkI R_OZT RjXj[ R^E]I ReJiQ R_RhRgW RbH^Q RcC`O RZORQ RdWgVdV',
	    'PoRIZI R^JkJ R[PlP R]YjY RS[W[ RWCW[ R]S]Y R_F_P RcRcY RgJgP RjSj[ RZPRR RiD^E',
	    'PoaEfE RRIZI R^PjP R\\PdP R`[h[ RS[V[ RWCW[ R`U`[ RhXh[ RhUlX RfLjP RgFkJ R^G[K RaReT RbI_P R\\UZZ RZPRQ',
	    'PoRIZI R\\NkN RZTlT RS[V[ RWCW[ R\\H\\T RaCaT RfCfT R\\HkHkT RgVl[ R`V[[ RZORQ',
	    'PoRIZI R[JlJ R[OlO R[TlT R[ZlZ RS[V[ RVCV[ R^J^T RdEdZ RiJiT RZNRP RjD`E',
	    'Po[FkF RRIZI R[MlM R]QjQ RcUjU Rb[l[ RS[V[ RWCW[ RdCd[ R]IjIjQ R]TZ[ RZPRQ R]VaZh[',
	    'PoRIZI R[KlK R\\OjO RS[V[ R`[c[ RWCW[ RcCc[ R]GjGjO ReSjX R\\Q_T RjQfU RcOfU R`VZY RZORR',
	    'Po[GlG RRIZI R_MhM RZTlT RS[V[ RWCW[ RcJcQ RhQh[ RfHlN R_W[[ RaH[N RcC_K RZPRQ R_Q^X',
	    'Po[GkG RRIZI R_NhN R\\RkR R`[c[ RWCW[ RcRc[ RhVkY RdGkN R^V[Y RaH[N RcC`J RZPRR RS[V[S[',
	    'PoRIZI R`JgJ R`[g[ RS[W[ RWCW[ RaUa[ RgXg[ RdCiHlJ R_H[J RcC]I RiVk[ RaReT R]MhMfR R]V[[ RZPRR',
	    'Po\\IkI RRIZI R[RlR RS[W[ RWCW[ R\\EdEdO RdHgLlN RcH_L[N RaP`S^Wl[ RZPRR RhTdY[[ RiD`E',
	    'Po[GkG RRIZI R[TlT RS[W[ RWCW[ RcCcG RcRc[ R_LaO RhMkP RfLbP R_I\\P RgIeO RZORQ',
	    'PoRI[I R_PiP R_ZkZ RS[W[ RWCW[ R]F]J R_J_[ R_JiJiP R_TkTk[ R]FlFlJ RdCeF RZPRR',
	    'Po\\FlF RRI[I R[LlL R]PjP R[SlS RS[W[ RWCW[ RdCdO R]IjIjO RbP_U R^WkZ R[PRQ RhUdY[[',
	    'PoRIYI RVCV[ R[LaL`[ RhTk[ RfScZ RhIhQjY RgOeX RdIkIkO RZORQ RS[V[S[ ReDcO RgMiW RbD[F[RZ[ RgMfV',
	    'Po]EjE R]HiH RRIZI R]LiL R\\PlP RR[V[ RVCV[ R]E][ RjTeV RbPdTiYlZ RZORR RcY\\Z',
	    'Po\\GlG RRIZI ReMlM R_ZkZ RS[V[ RWCW[ R_R_[ ReIeR R_RkRk[ RdCdG RZORQ R\\G\\R[Z',
	    'Po^HjH RRIZI R]KjK RgYjY RS[W[ RWCW[ R\\N\\Q R^R^Y RdNd[ R]DjDjK R^RjRjY R\\NlNlQ RZPRQ',
	    'PoRIZI R^KhK R\\TjT RS[V[ RWCW[ R\\O\\[ RaOa[ ReOe[ RcCiJlJ RcC\\K RZORQ R\\OjOj[h[',
	    'Po\\EkE R\\HkH RRIZI R[LlL R^SjS R^VjV Rf[j[ RS[V[ RVCV[ R^O^[ RcCcL R^OjOj[ RZPRR',
	    'PoRI[I R_LiL R`VhV RS[W[ Rg[l[ RWCW[ R]E][ R`O`V RdLdV RhOhV R]ElEl[ RaFbJ RgFfL R[PRR',
	    'PoRIZI RS[V[ RWCW[ RfVk[ R^UbY RhIkM RjHdN R\\T`V[[ R`I\\N ReQhY R\\HaJ ReHiJ RdEkEhK R[EbE_L R\\QbQ^Y RcQkQiVeYb[ RZORQ',
	    'Po\\EcE RRIZI R]QiQ RVCV[ R\\L[O RcGdJ R\\LkLkO RjFhJ R]G^J R_SdXk[ RiRdW\\[ RS[V[S[ RZPRQ RjD`E',
	    'PocFlF RRIZI R\\NiN R\\RiR RZVlV RR[V[ RVCV[ R\\J\\R RcCcJ RcRc[ R\\JiJiR RZPRR',
	    'Po\\GkG RRI[I R[NlN R^ZjZ RS[W[ RWCW[ R^R^[ RdCdG R^RjRj[ R_H`L RhHgM R[PRR',
	    'Po\\FlF RRIZI R`QkQ RS[V[ RVCV[ R`K`Z RkWk[ RcDeF ReVfZ RdX^Z RYORQ RjI_J RfIfOgX R\\F\\SZZ',
	    'PoWNgN RUQbQ RTTjT RRWlW RY[_[ RWJWN R_Q_[ RWJgJgN RSGkGkK RVDXF RhDeF R_C_G RSGSK RdP_Q RgPaQ',
	    'Po[IjI RRIZI R[MjM R\\SbS Rd[l[ RS[V[ RWCW[ R[D[M R\\P\\[ RePeZ R[DjDjM RlWk[ RkRdU RZPRR RbY[[',
	    'Po]GkG RRIZI R^WeW Re[i[ RS[V[ RVCV[ R^R^Y R^ReReW R\\OkOj[ RcIkL RdHaK\\M ReCcJ RZPRR',
	    'PoRI[I R^MhM R\\QiQ R]WhW RS[W[ Re[j[ RWCW[ R]S]Y RbLbW RhShX R_C\\K R_I]O RZORQ R^HlGlUk[',
	    'PoRI[I ReMjM R^UcU RgUlU R^ZlZ RT[W[ RWCW[ R^Q^[ RgPlPl[ RbC^L RcO^Q R[ORR RaFlFkM',
	    'Po\\GlG RRI[I R_KiK R_PiP R[TlT RS[W[ RWCW[ R_D_T RiDiT RgWl[ R`V[[ R[PRR',
	    'Po\\HaH RfHlH RRIZI RfNkN RfTlT RS[V[ RWCW[ RfCf[ RZORQ RaCaQ`W\\[ R[TaT R\\NaN',
	    'Po^HbH RRIYI RZMbM RS[V[ RVCV[ RgVb[ RfQkZ RjOeY RXPRQ ReKiW R[RZX R^C^[[[ RbRbV RdFgU RcFkFhU',
	    'PoSD^D RSK^K RSN^N RUQbQ RTTjT RRWlW RZ[_[ RSDSN RXEXH RXKXN RSG^G^K R_Q_[ RbEcIgLlN R`EjEiHeL`N RdP_Q RgPbP',
	    'Po]GjG RRIZI R[KkK RS[V[ RWCW[ RcCcK RfKfOkNlO R`M]P RbP\\V R_ScXj[ RZORR R`SiSeWaYZ[',
	    'PoZGlG RRIZI RVCV[ R^L^[ RcCcG ReTk[ ReNgQ RhTaZ RbOfW RcLkMfV R_I[Q RdI`S RZORQ RS[V[S[',
	    'Po\\ElE RRI[I R\\KaK RfSkS R[[l[ RS[W[ RWCW[ RaEaK R\\K\\SaSaZ RfSfZ RfEfKkKkS R[PRR',
	    'PoRI[I R]JlJ R`RkR R_ZlZ RS[W[ RWCW[ R_U_[ R`M`R ReKeZ RlMlR R]DlDlJ RlUl[ RZORQ R]D\\Q[Z',
	    'Po^EdE RRIZI RYPlP R]UjU RS[W[ R_[c[ RWCW[ RcKc[ R]KjKjV RbGcJ RiFkJ R^E[K R[PRR RiD`E',
	    'Po[ElE RRIZI RS[W[ Rh[l[ RWCW[ R\\J\\[ RdEd[ R\\JlJl[ RhOfV R`O^V RZORQ R`NaU RfNhNiU R`N^M',
	    'Po[GdG RRIYI RZMdM R[SdS RVCV[ R_C_M R_O_Y RfCf[ RfLiNkP RZORQ RS[V[S[ RdXYY',
	    'PoRI[I R]KbK ReKjK R]QjQ Re[l[ RS[W[ RWCW[ R]E]Q RfQfZ RdEjEjQ RlXl[ R_V\\ZY[ RcD\\E R`Q^X R[PRR',
	    'Po\\HlH RRIZI R]RcR RS[W[ RWCW[ R]L]R R]LcLcR RhDjF RgU`[ RjLeW RZORR RdU[V RfCeLgY RlWk[i[',
	    'PoRI[I RS[W[ RWCW[ RiEhI RiQhU RcJkN RcNdUgYl[ R_F]I R^Q]U RcJ`M\\N RcSbW`Y[[ RcCbK R[PRR',
	    'PoRI[I R]LkL RdSjS Rd[l[ R\\G\\K RdCdG RdLdZ R\\GkGkK R_VcZ R^S`Y R^T[[ R^Q\\Y R[PRR RWCW[S[',
	    'Po[GlG RRI[I R^RjR RS[W[ R`[d[ RWCW[ R^K^Q RdCdG RdRd[ R^KjKjQ RhUlY R_U[Y R[PRR',
	    'PoRIZI R[QkQ RS[V[ RWCW[ RcMc[ ReRlY RcPeS RbR\\X R\\H^L RcGcL RjGhN RZORQ RjDcE\\F',
	    'PoRIZI R[QkQ RS[V[ RWCW[ R\\E\\I RcNc[ R\\EkEkI RfGfMkMkK ReSkY RbS\\Y RaH_L\\M RZORQ',
	    'PoTF`F RRHaH R]O_O RgOjO RVQbQ RTTkT RRWlW RZ[_[ RTKTO RYCYO R_Q_[ RTK_K_O RdDdL RkDkO RUCSG RfP`Q',
	    'PoRIZI R_[b[ RS[W[ Rh[l[ RWCW[ R\\DbDb[ RfDlDl[ R^PaS R^J`L RgQjS RhJjL RZORQ R\\D\\Q[Y RfDfTd[',
	    'Po\\FkF RRIZI R[MlM R[QlQ RS[W[ RWCW[ RdCdG RhGgL R`HaM^Vk[ RZPRR RhQfVdX[[',
	    'Po^IjI RRI[I R^MjM RS[W[ Rf[j[ RWCW[ R^E^M R^EjEjM R`OZU RbT\\Y RfWa[ RhQdY RbQ`W R[PRR R`M^RlQk[',
	    'PoRIZI R]SkS R[[l[ RS[V[ RWCW[ R\\G\\J RcCcG RcScZ R\\GlGlJ R_N\\O RfGfNkNlL RaG`M RZORQ',
	    'Po^IlI RRIZI R^NkN R^TkT R^ZlZ RS[W[ RWCW[ R_G_[ ReIeZ R`C\\L RfCeJ RZPRR',
	    'Po[GlG RRIZI R]QiQ R]ViV Rc[k[ RS[V[ RVCV[ R^L^W RdJd[ R\\MjMjV RlXk[ RfHlN RaH[N RbC_J RYPRR',
	    'Po\\GlG RRIZI R[MlM R^UjU R^ZjZ RS[V[ RWCW[ R^P^[ RaCaM RhChM R^PjPj[ RZPRR',
	    'PoZElE RRIZI R\\QbQ RS[V[ RWCW[ R\\E\\W RbEb[ RgWl[ RhVd[ ReQiY RjPfY RdLhW R\\KkKhW RZORQ RbVYW',
	    'PoRIZI R\\QiQ Re[j[ RS[V[ RVCV[ RbJbZ RbQhV RcQ[X R^K^O RgKfO R_C\\K RZPRR R^HlGlTk[',
	    'Po[FbF RRIYI RZKkK RVCV[ RbOb[ RgMlR RfKhO R_MZR ReSeX RhRkX RbG^P R]S[X RZORQ RS[V[S[ R^[a[^[ RiD_E',
	    'Po]EgE RRI[I R]LlL R_SlS R_WlW RS[W[ Rh[l[ RWCW[ R]E^R_[ R_OlOl[ R]HlHlL RZORQ R]O[Z RkDdE',
	    'PodFkF RRI[I R\\JlJ R^RjR R^VjV Rg[j[ R^N^[ R_E_J RdCdJ R^NjNk[ R[PRR RWCW[S[',
	    'PoRKYK RdKkK RRQYQ RcQlQ Rd[h[ RhEh[ RZMaMaY]Y R`EbM R\\EZM RkDcE RYDRF RVEVRUX R\\M[X',
	    'PoUEcE RTGjG RRKlK RZN_N RST]T R`TkT RRW^W R_WlW RU[X[ Rb[f[ RXQX[ R_D_N RfQf[ RhDbD R\\PUQ RiPbQ',
	    'Po^HjH RRI[I R^LjL R^XfX Re[j[ RS[W[ RWCW[ R^D^L R^Q^W R^DjDjL R_OZT RZPRR ReS^U R`M^QlQk[',
	    'PocEjE RRIZI R^LhL RS[V[ Rf[j[ RVCV[ RcCbOkNkO R\\IlIjK RcWaZ][ RZORQ RdQcX R^TkTj[ R\\H\\QZY',
	    'Po_IiI RRIZI R_OiO R^UjU R\\YlY R\\D\\[ RdIdU R\\DlDl[ RfQiS RZPRQ RVCV[R[',
	    'PoRIYI R_JgJ R\\VaV RVCV[ R\\N\\X R\\NaNaV RdNd[ RcCiJlK RcC[K RZORQ RS[V[S[ RdNjNjWgW',
	    'Po\\FlF RRIZI R^NlN R_SjS RS[V[ RWCW[ R_JjJjS RfVk[ RcV^[ ReHdSgX RdCdF RZPRR R\\F\\R[Z RdMcV',
	    'Po[IcI RRIYI RZPdP RR[U[ RVCV[ RfDf[ R\\WY[ R_I_Q`TcY R\\D]G RbD`I R^Q[Y RYORQ RfElEiMlRkVhV R_L]V',
	    'PoRI[I R]MkM R]RkR R[VlV RS[V[ RWCW[ R]I]R RdId[ R]IkIkR R_DaG RhCfI RZPRR',
	    'PoRIZI R\\JlJ R`PdP RZ[l[ RS[V[ RWCW[ R\\R\\Z RaRaZ RdHdO RfRfZ R\\RjRjZ R^DjEdG RZPRR',
	    'Po`HjH R\\HfH RRIYI R[LlL RS[V[ RVCV[ ReLjP RfEjI RbC\\H RaK\\P RbH_N RcQ\\T RfS]W RYQRR RiVdY\\[',
	    'PoRI[I R]UjU R_ZkZ R\\ZgZ RS[W[ RWCW[ R]P]U RcMcZ R]PjPjU RhWl[ R^FbJfLlM R\\EjEeIaL[M R[PRR',
	    'PoZHlH RRIYI R\\LiL RS[V[ RVCV[ R\\O\\W R\\OjOjW R]DjDjL RdXk[ RYORQ RbX[[ R]E\\L RdDcL RcRbW',
	    'PoRIZI R\\IlI RfTkT R[ZlZ RS[V[ RWCW[ R]L]V RaIaZ RfIfZ RkLkV R_D`H RhDgH RZORQ RaT\\U',
	    'PoRIZI R]JgJ RZMkM RS[V[ RWCW[ RgTlY RiPfS RdMhV RaNZQ RbRZU RbPdUaWZZ R_C^J R_FhFfM RZORQ RcRdWd[`[',
	    'Po\\GlG RRI[I R\\NlN R`ZjZ RS[W[ RWCW[ R`Q`[ R`C`J RhChJ R_SjSk[ R`Q[V RcK_S R[ORR',
	    'Po[GkG RRIZI R\\RjR RS[W[ RWCW[ R\\J\\R RcCc[ R\\JjJjR RcQkY R^WZY RcQ\\X R_M`P RgLfP RZORR',
	    'PocFkF RRI[I R]NjN R]RjR R]VjV RS[W[ RWCW[ R]J]V RcCcJ R]JjJjV RfXkZ RaXZ[ R[PRR',
	    'Po[HlH RRIYI RfRkR R\\VkV R\\ZkZ RS[V[ RVCV[ R\\M\\[ RfGfR R\\MkMk[ R_D`G RhEeH RaHaP^S RYPRR',
	    'Po[IlI RRIZI R_[b[ Rg[k[ R\\L\\[ R\\LbLb[ RfMfV RkLk[ R]PaQ R]T`U R^D_G RhDgG RZORQ RWCW[S[',
	    'PoRIZI R_KgK R_[b[ RVCV[ R\\N\\[ R\\NbNb[ R^Q`R R]U`V RcD\\K RdTg[ RiUk[ R]EcDhIlK RgNdT RkNhT RZORQ RS[V[S[',
	    'Po`ElE RSE^E R_IlI RUN]N RUTjT RRVlV R[[_[ RVHVN RUJ]I]N RUQ`Q_[ RiEiN RbK_N RUHRL RVEUK RcFbL RdP_P RgPaP',
	    'PoRIZI R^NhN R[SkS RS[V[ RWCW[ RgIlN RiEfH RlHhJ R\\G`I[N ReDhK RcNcTeWgYk[ R\\EbE_J RbV_Y[[ RZORQ',
	    'Po[FlF RRIZI R_QhQ R^UhU Rh[k[ R\\K\\[ RcCbK RcQcY R\\KkKk[ R`MaO RfLeQ RZPRR RVCV[R[',
	    'PoRI[I R^KgK R\\MiM R^PgP R^RgR R^YgY RS[W[ RWCW[ R^U^Z R^UgUgY R_C\\K RZORQ R^HlHlUj[g[',
	    'PoRIZI RaPeP R[SkS RS[V[ RWCW[ RcQc[ ReIeO R\\JlJhM RcShXlY R`GgJ R^EiEcH RdJ`N\\O RcS^XZY RZPRR',
	    'Po\\ElE RRI[I R^MjM R\\UlU R\\ZlZ RS[W[ RWCW[ R\\P\\[ R^H^M RdPdY R^HjHjM R\\PlPl[ R[PRR',
	    'Po]IjI RRIZI R]NjN R[QlQ RS[V[ RWCW[ R]D]M R^Q^[ RdDdM R]DjDjM RcQeVhYlZ RkTfV RZPRR RdY]Z',
	    'PoRIZI R]JjJ R]OjO R_[h[ RS[V[ RVCV[ R]E]O R_R_Z RcEcO R]EjEjO RbQeU RhWg[ RiSlY R[SZY RYPRR',
	    'Po[FkF RRIZI R\\JjJ R[MlM R_QfQ R]UiU RR[V[ RVCV[ ReMjS R`MZT RcWiY RbH^P RcRaW_Y[[ RcC`N RYPRR',
	    'PoRIZI R_JiJ R^RiR R^WiW R[ZlZ RS[V[ RWCW[ R\\G\\J R^N^V RdCdG R^NiNiV R\\GkGkJ RZORQ',
	    'Po\\HlH RRIZI R]TkT R]ZkZ RS[V[ RWCW[ R]N][ R`C`K RdNdZ RhChK R]NkNk[ RZPRR',
	    'Po]HiH RRIZI R]LiL R\\OkO RdTjT Rb[l[ RS[V[ RWCW[ R]D]L RdOdZ R]DiDiL R\\Wb[ R]T[Z RZPRR',
	    'PoRI[I R]JbJ ReJkJ R]NkN R\\SkS R[ZlZ RS[W[ RWCW[ R]E]N RcScZ ReEkEkN R[ORR RbD]E',
	    'PoRIZI R[JlJ R\\SaS RfSkS R\\ZkZ RS[V[ RWCW[ R\\N\\[ R]FdEdY RfNkNk[ RaM\\N RZORQ RjDaE',
	    'PocHlH RZHaH RRIXI RgJgW RdLdZlYl[ RYORQ RkMbQ RdCbK RVCV[S[ RkMkUiU R_[\\[ R^C]H\\PZY R\\M`M`V_[',
	    'PoRIZI ReQiQ RS[V[ R`[g[ RWCW[ R`T`[ RgYg[ ReLaQ RaJ\\P RjVlZ RaSeU R_C\\J RbF_M R]U[Z RgFcO R^JhM RZORQ R^GkGjQ',
	    'Po\\FlF RRIZI R_JhJ R_MhM R[QlQ R]ZlZ RS[W[ RWCW[ R]Q][ R_C_Q RhCgQfVkUkV RZORQ RbQ`V^W',
	    'Po^IjI RRI[I R_PjP R_TjT R^XjX R[XgX RS[W[ RWCW[ R^D^I R_L_X R\\LkLj[ R^DjDjI R[PRR RlWhW',
	    'Po]FbF RRIZI R\\IlI R`RkR R`VkV R`ZkZ RS[V[ RWCW[ R`N`[ ReEeN R`NkNk[ RZPRR R\\F\\R[Z RjD`E',
	    'PoRI[I R]JjJ R]NjN R\\QkQ R]VjV R[[l[ RS[W[ RWCW[ R]F]N RdQdZ R]FjFjN RcCcF R[PRR',
	    'PoRIZI R\\KkK R]UlU Ri[l[ RS[W[ RWCW[ RbObZ RgOgZ R\\FkFkK R\\OlOl[ RcCdF RZPRR R\\F\\R[Z',
	    'Po^HjH RRIZI R^LjL R\\OlO RS[W[ Re[j[ RWCW[ R^D^L R^DjDjL RaO[U RbT\\Z RgSb[ RZPRR R^SkSj[',
	    'PoRIZI ReNjN R[TlT RS[V[ RWCW[ R]I]R ReJeO R\\JjJjR R`E[J RcSeWgYl[ RaJ`N R_FfFdJ RbV`Y[[ RcQbX RZORQ',
	    'Po`HgH RRIZI R^PiP R[UlU RS[W[ RWCW[ R^K^Q RgSg[ R^KiKiQ R`W\\[ RdCgGlI RcC_G[I R[ORR R`S_Y',
	    'PoRIZI RdPlP R^ViV R^ZiZ RS[V[ RVCV[ R^R^[ RgCgN R^RiRi[ RaK[Q RgHlJ R_C\\J R^GcF`N RZORQ R]G_LbOjP',
	    'Po\\GkG RRIZI R\\MlM R^UjU R^ZjZ R^Q^[ RcCeG R^QjQj[ RhHfM RZPRR RWCW[S[ R_H`L',
	    'PocHiH RRIZI RbMlM R]SjS R]VjV RS[V[ Re[j[ RVCV[ R]O][ RcDcM R]OjOj[ R[EkEkG R^G[N RZPRQ R^IaLhM',
	    'Po\\ElE RRIZI R`KjK R`NjN R^QlQ R\\YlY RS[W[ RWCW[ R\\E\\[ R`H`N R`HjHjN RdNaTjW RZORQ RiRfV_W',
	    'PoRIZI R\\JkJ R\\MlM R^RkR R^VjV R\\ZlZ RS[V[ RWCW[ RdSdZ R\\EkEkJ RgNkR RbM`R RZORQ R\\E\\Q[Z',
	    'PoRIYI R^ZiZ RR[V[ RVCV[ R[F[I R^S^[ R\\TiTi[ R[FkFkI RbCcF RaI\\M R_KdPlT R`KhKcPZT RZPRR',
	    'PoRIZI R\\JkJ R[MlM Rh[k[ RS[V[ RVCV[ R\\R\\[ R\\D\\J RaRa[ RcCcJ RfRf[ RkDkJ R\\RkRk[ RbMaT RZPRR',
	    'PoRIZI R\\JcJ R\\OcO RiVlV R\\E\\Y R\\EcEcO RfEf[ RfElElV RaRdY RbV[Y RZPRR RWCW[S[',
	    'Po\\FkF RRI[I RS[W[ RWCW[ R\\L\\O R^Q^X RdCdF RdNd[ R^QjQkX R\\LlLlO R`G`K RhGgK RZPRR RgYjYgX',
	    'Po[GeG RRIZI R\\QdQ Rh[l[ RS[V[ RWCW[ R\\K\\Q R`C`[ R\\KdKdQ RgFgT RlDl[ R`RcV R^TYY R`P]V RZORQ',
	    'PoREaE RTK`K RgOkO RVQfQ RUTjT RSWlW R[[`[ RTGTK RZCZO R`Q`[ RTG`G`K ReDeM RkCkO RYK`N RZJWMRO',
	    'Po\\FlF RRIZI R^MiM R^SiS R`[d[ RS[V[ RWCW[ R\\P\\S R^I^M RdSd[ R^IiIiM R\\PlPlS RcCdF RZPRR',
	    'Po]HbH RRIZI R]ViV R]ZiZ RS[V[ RWCW[ R]C]N R]Q][ R]QjQj[ RbOaQ RjFdI ReCeNlMlN RZORQ RcM[N',
	    'PoRIYI RZKbK R_D_[ R_NaQ RfSb[ R_LZW RkIjP R^I[U RgCgPdY RYORQ RVCV[S[ RdIcO RbD[E RgJiX RgKeW',
	    'PoSH]H RTTjT RRWlW RZ[_[ RYDYO RUQ`Q_[ RYHSN RkFhI RdDeJhMlO RXJ]L R`F^J RdFcJaM\\O ReP_Q RgPaP R\\DSE',
	    'PoRIZI R[RlR RS[V[ RWCW[ R[F[J RcCcG R[FkFkI RcQeWgYl[ R_L\\N RbV_YZ[ RfOkP RfFfMkLkM RaF_L RZORQ RcNbW',
	    'Po]HkH RRI[I R]LkL R^XhX Rf[j[ RS[W[ RWCW[ R]D]L R^Q^Z R]DkDkL RcTgV R_OZT RcS`W RZPRR R^QlPlUk[',
	    'Po]IkI RRIZI R]PjP R]TjT R[WlW RS[V[ RWCW[ R\\D\\G R]L]S RdGd[ R]LjLjS R\\DkDkG RZORQ',
	    'Po`EkE RRIXI RcKkK R_ZlZ RR[U[ RVCV[ R]C][ R_H`L RdSfU ReOgP RXORQ R[HZN RdEaT RjMhZ',
	    'Po[HlH RRIZI R\\OjO R\\SjS R\\WkW RZZlZ RS[V[ RWCW[ R\\K\\S R[EcEdZ R\\KjKjS RZORQ RiCaD',
	    'PoSJ_J RSM_M RhPkP R]P_P RTTkT RRWlW RZ[_[ RSGSP RYCYG RUR`R_[ RSG_G_P RdEdM RkDkO RTDVF R^E[G ReQ_R',
	    'PoZGlG RRIZI RbLhL R]OkO R[SlS RR[V[ RVCV[ R]D]O RbCbK RcOc[ RhChK RhXlZ RcSjY RcS^XZZ RZPRR',
	    'PoRIZI R[TlT RVCV[ R_C_P RdEkEkN RcReWgYl[ RbV_YZ[ RcQaW RgFeM RbE[G RbH[J RcLZN RZORQ RjOgO RS[V[S[',
	    'PoRIZI R\\KkK R[NlN R_RiR RS[V[ RVCV[ R]F^I R_SdXk[ RiFgJ RiRdX^[ RZPRR RiCbE\\E RcEcI R`K_R]WY[',
	    'PoRIYI R`JlJ RbMjM R[N_N RaQkQ R`UlU Rb[l[ RS[V[ RVCV[ RfCfY RbFjFjM R]WY[ RZF^F[N R^S[Z RYORQ R_O\\X R[R]W`Zh[',
	    'PoRIZI R\\KbK R\\PbP R\\E\\V RZEcEb[ ReEe[ ReElEhL RZORQ RWCW[S[ RkVhV RjNlSlV RbVZW',
	    'Po\\IlI RRIZI R]Q`Q RgQkQ R]UkU RS[W[ RWCW[ R]M]W R]EdEd[ RfMkMkW RaL\\M RZPRR RiDaE',
	    'Po\\GkG RRIZI R^SiS R^WiW R[[l[ RS[W[ RWCW[ R^O^Z RcCcN R^OiOiZ RcFiLlM RcG^L[M RZPRR',
	    'Po\\FaF RRI[I R^RjR R^VjV RS[W[ Re[j[ RWCW[ R^N^[ RaCaL R^NjNj[ RfCfLlKlL RkFeG RaI[J R[PRR',
	    'PoRI[I R\\MkM R[RlR R]YjY RS[W[ RWCW[ R]T][ RcGcJ RdMdY RkTk[ R^G^K RjFgM R[PRR RjDbE\\E',
	    'Po[GkG RRIYI R]KdK RhDjE R^UcV^Z RiPeZ RYPRR RlXk[ RjKgW RaK^T R]OdObW RVCV[S[ RgCgMiY R[G[SY[',
	    'Po[ElE RRIYI R_NjN RS[V[ R_[c[ RVCV[ R`H`N RcOc[ R^IjIjN ReSkZ R_TZZ RjQfU RaF[K RcOfU R[R`R^W RYPRR',
	    'PoeGlG RRJYJ R\\D\\K R]O]V R`C`L R]OiOiV RfJjK ReDcJ RdVd[lZl[ RYPRR RVCV[S[ R`YZ[ RcQbX',
	    'Po]IiI RRI[I R]NiN RY[l[ RS[W[ RWCW[ R\\R\\[ R]E]N RaRa[ ReRe[ R]EiEiN R\\RjRj[ R[PRR',
	    'Po[HbH RRIXI R[LaL RZQbQ RS[V[ RVCV[ R^L^Y RgRkZ RgQcZ R]DYK RYPRQ RbW]Y RgOiX RdIlHjN ReDcL RgMeW',
	    'PoRIZI R]MkM ReRlR R^H^M ReMeR R\\IkIkM RaD\\I RaTgV R`EgFdHbN_QZS R]Wh[ RZPRR RVCV[S[',
	    'PoRIZI R`XhX Rh[l[ R\\H\\[ R`R`Y R`RhRhY RbDlDl[ R\\D_G RbH^L RaJeOjQ RaIhJcO]Q RYORQ RVCV[S[',
	    'Po[IkI RRIZI R[SlS RcCcO R^D_G RiDhH RcHgLlN RaP_U RcH`L[N R^WkZ RYPRR RVCV[R[ RgTcY[[',
	    'PoRIZI R^PjP Ra[i[ RS[V[ RWCW[ R^I^P RaSaZ RiWi[ R^IjIkP RcRfU R_DaG RiTlY R^T[Y RhCfH RZORQ',
	    'Po`EkE RRIXI RaOkO RaVkV R_ZlZ R\\C\\[ R^I_M RaIaU RaIkIkU RXPRR RVCU[R[ RZIZO',
	    'PoRIXI Rd[l[ RZHZK R]L]U ReTeZ R]LhLhU RZHkHkK RlXlZ R\\D^F RbCcF RiCgH RYORR RbU_YZ[ RVCV[R[ RcObV',
	    'Po\\FlF R\\IkI RRIZI R[LlL R]SjS R[WlW Rg[j[ RS[V[ RWCW[ R]O][ R`C`L RdLdV RgCgL R]OjOj[ RZORR',
	    'PoRIZI R[RkR RVCV[ R_U_[ RdUjZ RcRfW RcHdJ RcDgGlJ R]LiLfP RcC_H[J RbR`UZW RjTeV RZPRQ RS[V[S[ ReY^[ RcPcT',
	    'PoRIZI R_ZiZ RS[V[ RWCW[ R\\F\\J R_S_[ R_SiSi[ R\\FkFkJ RfJjN RcCdF RaJ]N RcLhQlS RdL^R[S RZPRR',
	    'PoRIZI R_MkM R_QkQ R_UkU R_ZlZ RS[W[ RWCW[ R\\G\\J R_L_[ RfKfZ R\\GlGlJ RfIeO RcC_L[PRQ',
	    'PoRI[I R^LhL RgOjO R^XhX RS[W[ Rg[j[ RWCW[ R^T^W R^G^K RhHhK RhThW R`N_Q[T R_C]H RbM]O RbY][ RZORQ RcGbM RcSbY R_FlFkN R^RlRk[',
	    'Po[FbF ReHkH RRIYI RcOiO RS[V[ Rh[l[ RVCV[ ReFeK RcThTh[ R_KkKkN RlYl[ RYPRR RcTaZ_[ ReMeRkQkR R[F[RZY R_K_S^Z RiD`E RfN`O',
	    'Po]IkI RRIZI R^MkM R\\SlS RS[V[ RWCW[ R`R`[ RbJbR RhJhR R]DkDkI RfVkZ RjUgW ReRgX RZPRR RfY_Z R]D]Q[X',
	    'Po^IjI RRI[I R]OkO R]SkS R]WkW RS[W[ RWCW[ R]L]W R^D^I R^DjDjI R]LkLkW RgXl[ RaX[[ R[ORR',
	    'PoRIXI RaQkQ RR[U[ Rc[f[ RY[\\[ RUCU[ R]K][ RfQf[ RhJaQ ReEcJ RiTkY RiNkS RcT`Z RZF_E\\L RXORQ R_PYR RjDaE RgKaK',
	    'Po\\FlF RRIZI R]MkM R]PkP RZTlT RS[V[ Rc[g[ RWCW[ R]I]R RdCdQ RgRg[ R]IkIkR R^VaY RfDkE RZORQ',
	    'Po[FlF RRIZI R[OkO R]VjV R]ZjZ RS[V[ RWCW[ R]R][ RdRdZ R]RjRj[ RgLlP RdK`O RgHbM RcE_J R]JcL RZORQ',
	    'Po[FlF RRIZI R[KlK R^OiO R^SiS RbXjX R^XgX R[XdX RS[W[ RWCW[ R^K^X R`C`I RhChI RiKi[ RZORQ',
	    'Po\\FlF RRI[I R\\PlP RS[W[ Rd[h[ RWCW[ R\\K\\O RdCdF RdMdP R\\KlKlO R`W\\[ R`GaK RhGgJ RZPRR RaUjUh[ RaP`X',
	    'Po\\GlG RRIZI R]KkK R[OlO R`SkS R^[l[ RS[V[ RWCW[ RdGdO ReSeZ R_DaG R_Q[Y RhCgG R_O]W RZORQ',
	    'PoRI[I R]UjU R_ZkZ R\\ZgZ RS[W[ RWCW[ R]P]U RcMcZ R]PjPjU RhWl[ RbFdG R]G\\J R\\EiEeJ]M R^FbJfLlM R[PRR',
	    'Po\\GjG RRIYI R[KlK R[PjP RYZlZ RS[V[ RVCV[ R\\S\\Z R`S`Z RcCcK ReSeZ R\\SjSjZ RfLkQ RaK[P RZPRR',
	    'PoRIZI R_PjP R[TlT R]ZkZ RS[V[ RWCW[ R]V][ RdPdZ RkVk[ R_N^R R]IdL RdC`GgJ RaG[I RbFkFfK]O RZORQ',
	    'PoRIZI R]IjI R]MjM R]PlP R[YfY RS[V[ Rf[j[ RWCW[ R[U[Z RaSaX RfUfY R]FjFjM RbCaF RZORQ R]F]SlSj[',
	    'PoRIZI R\\IlI R`QkQ R^VkV R^ZkZ RS[W[ RWCW[ R^S^[ R`K`P RbCbI R^SkSk[ RkDgH R]FhF[M RZORQ RjL_M',
	    'Po]EkE R`HiH RRIZI R]KlK R^TlT RS[V[ Rd[h[ RVCV[ R`K`Q RhQh[ RaVcX ReLhPlQ RdP_P RkLfN RZPRR R]E\\P[X',
	    'PofHjH RRIZI R]TjT R]WjW Rf[j[ RS[V[ RVCV[ R]Q][ R]QjQj[ R\\EkEjH R_J\\O RcDaG]I RhJfNcN RZORQ R[KbKbO_N RdKlKkOhN',
	    'Po[HkH RRIYI R[OlO R\\SjS R`H`[ ReGe[ R\\KjKjS RdSlZ R]D`F RaS^VYZ RgCeH RYPRQ RVCV[S[',
	    'PoRI[I R]JaJ RfJjJ R]NjN RS[W[ RWCW[ R]F]N RcCcR RfFjFkN RaE]F R^SbWfZl[ R[PRQ R\\SjSfWbY[[',
	    'PoRI[I R]IjI R]MjM R[UlU RS[W[ RWCW[ R]FjFjP RcCbF R]F]PhQiS RcSdWgYl[ RcU`YZ[ RZPRR RcQbW',
	    'Po[FlF RRIZI R_NiN R`XhX RS[W[ Rh[l[ RWCW[ R\\P\\[ R_I_M R`S`Y RdCdG R`ShShX R_IiIiM R\\PlPl[ RZPRQ',
	    'PoeElE RZGcG RRIYI R[NcN RdNlN R[RcR RZVdV R[J[R R_C_[ R[JcJcR RhEh[ RVCV[S[ RYPRQ',
	    'Po[IdI RRIYI RfKlK RfRlR R[ScS Ri[l[ RS[V[ RVCV[ R[L[U RcLcT RfDlDl[ R\\C]G RbCbH RYORQ R_I_S^X RfDfSdY',
	    'PoRIZI R[TlT RS[V[ RWCW[ RcRc[ RdNkQ R\\MbP RcShXlZ R[LbL^PZQ RdLkLhOdQ RcS^XYZ R^FiI R]EhEcH\\J RZORQ',
	    'Po\\EkE RRIZI RdLkL R[NlN R]VjV R]ZjZ RS[V[ RWCW[ R]R][ R]RjRj[ RiJlL R[LbKbL R_G\\L RgGeL RZORQ',
	    'Po\\HlH RRIYI R^LdL RS[V[ RVCV[ RaL`UcW RhCkF RhUcZ R^O^S RcOcS R^Y\\Y RkKhV RkVk[ RYORQ RfCgLfOhZ R\\H[RZY RaO`W',
	    'PoRIYI R^IhI RZZlZ RS[V[ RVCV[ R]T]Z RaTaZ ReTeZ R]TjTjZ RaNgNlS RgEkI R^OZS R_E[I RZORQ',
	    'PoRIZI RbLjL RbVkV Ra[l[ RVCV[ RbGbX RfCeG RbGjGjL RbPkPkX R\\J^L R\\D_F R[P^P^XZ[ RZORQ RS[V[S[',
	    'PoRIYI R\\JbJ ReJlJ Rf[j[ RVCV[ R[DbDbJ ReDlDlJ R^P[S R_T[X RgRcS RiTgVcX RYORQ RS[V[S[ Ra[][ ReJdOlOlTk[ R\\J[ObObTa[',
	    'PoRIYI R[KlK Ri[l[ R`[c[ RS[V[ RVCV[ R]NcNc[ R[FlFlK ReNlNl[ R^QaS RfQiT RcCdF RYPRR RcT\\W RlTeW R[F[RZY',
	    'Po]HjH RRI[I R]LjL R_[c[ Rh[l[ R]D]L R\\OcOc[ R]DjDkL ReOlOl[ R_P\\T RaS\\Y RiPeT RlSfX RZORQ RWCW[S[',
	    'PodGkG RRIYI RbMlM R[SkS RVCV[ RcPc[ RdGdL RiDiP RcRhXlY R]GbG_N R^D\\I`LZPRQ RcR]XZY RS[V[S[',
	    'Po\\FlF RRIZI R^MlM R^QjQ R^ZjZ RS[V[ RWCW[ R^T^[ RdCdF RdGdS R_JjJjQ R^TjTj[ RZORQ R\\F\\RZZ',
	    'PoRJ^J RZP]P RURcR RTTjT RSWlW RZ[_[ RUEUM RYKYN RUE]E]P R_R_[ RbDaH RhHlGlH RYFYH RbJcMjP RaJjJgMaP RbDhDhH RgQbQ',
	    'PoRIXI R^[a[ R^R^X R]C\\GaGa[ R]J^M RXORQ RVCV[S[ RaOYP R[G[SY[ RdEdL RdEiEjMkMmK RdPkPgWc[ ReQgWk[',
	    'Po[FlF RRIZI R^ZiZ RS[W[ RWCW[ R^S^[ R_C_I RgCgI R^SiSi[ RcIiO RcI\\P RZORQ R`PeP',
	    'Po[ElE RRIZI R\\OjO R[SlS RS[V[ RWCW[ R\\I\\N RaEaN RcOc[ RfEfN R\\IjIjN RcRjY RcR^XZY RZPRR',
	    'PoRI[I R]TbT ReTkT R]ZkZ RS[W[ RWCW[ R]O][ RcGcK ReOkOk[ RjGkK R^E[L RbM]O R[PRR RiC^E',
	    'PodGjG RRI[I R_RjR R_VjV RS[W[ Rf[i[ RWCW[ R\\K\\O R_O_[ R_D_K RdGdK R_DjDjK R_OjOj[ R\\KlKlO RZORR',
	    'Po]IkI RRIZI R_MkM R^RlR R^VkV RS[V[ RWCW[ ReRe[ R]DkDkI RdJeM RaNaQ RhNgR RZPRR R]D\\Q[Y',
	    'PoRIYI RS[V[ RVCV[ R[F[I R]NcNcY R[FlFlI RbDdF ReHkK RaH\\K RYORQ RfUgZaZ RhMjY R]P[X RiMaN',
	    'PoRIZI R^JkJ R^NjN R\\QlQ R^ZjZ RS[V[ RWCW[ R]F]I R^T^[ RdHdT R^TjTj[ R]FlFlI RcCfF RZPRR',
	    'PoUIiI RVLhL RROlO RWUgU RUWiW R[[_[ RSFSI R_C_F RYR_R_[ RSFjFjI ReGdOgRlT RZGZOWRRT RcQ\\R',
	    'PoRIZI R]NjN RZZlZ RS[V[ RWCW[ R\\R\\Z R]E]N RaRaZ RfRfZ R]EjEjN R\\RjRkZ RdJgL RcFaK RZORQ',
	    'PoRIZI R]OiO R]RiR R^ZjZ RS[V[ RWCW[ R]L^U^[ R]LiLiR R^UjUj[ RdCiH R^HZJ RcC]I R_HgHlI RZPRQ R]O\\X',
	    'Po\\FkF RRIZI R]LiL R]OiO R]SiS R[VlV RS[V[ RWCW[ R]I]V R]IiIiV RhXk[ R`Y[[ RcCbI RZPRR',
	    'Po[FkF RRIYI R\\JiJ RZMlM R[TjT RS[V[ RVCV[ RcPc[ ReMjR R`MZS RdVjY RaH^P RYPRR RbT^XZZ RbC`M ReP^Q',
	    'Po\\ElE RRIZI R^LjL R_TiT Rh[k[ RS[V[ RWCW[ R\\O\\[ R^H^L RdTd[ R^HjHjL R\\OkOk[ R`QbS RgQdU RZPRR',
	    'Po]GlG RRIZI R^JkJ R^MkM R^PlP RS[V[ Rf[k[ RWCW[ R^F^P ReEeP R_C\\I ReCdG R\\SfSfVlVk[ RZORQ R`S_W[[',
	    'PoVNgN RSS]S R`TkT RRW^W R_WlW RU[X[ Rb[f[ RVJWN RXPX[ RfQf[ RVJgJgN R_DiI RZHcGlI R_DXHRI R\\PUQ RiPbQ',
	    'Po\\HjH RRIYI R[LkL R]SiS R[WlW R_[c[ RR[V[ RVCV[ RcPc[ R]E^F RfLjP RiDgF RaK\\P RYORR RcCaL RfO_P',
	    'PoZFkF RRIZI R\\RiR RS[W[ RWCW[ R_C_I RcNc[ RgCgI RcRjY RcR[Y RcIiOlP RcI]PZPRQ',
	    'PoRIZI RfLlL R_PlP RcSkS RcWkW RS[V[ RWCW[ R\\F\\I RcPc[ RdCdF RfFfL R\\FlFlH R`O[T R`I]L RaF`K RaL_Q RZORQ',
	    'PoRKZK RdKkK R\\LaL RcQlQ R\\YbY Rd[h[ R\\Q\\Z R\\QbQbZ RhEh[ R_DbJ R^DZL RkDcE RZPRR RZDRE RVEVRUX',
	    'PoRIZI R]KiK R]OlO RS[V[ Rf[j[ RWCW[ R`U`Y R]FiFiK RfUhW RcCbF RcUdX R\\VZZ RZORQ R]F]SkSj[',
	    'Po_IhI RRIZI R\\QjQ R_[h[ RS[V[ RVCV[ R\\E\\Q R_T_Z R\\EjEjQ RcSeV RcJgO RhWh[ RiSlY R\\TYY RaN_N RcGbM RZPRR',
	    'Po\\DlD R_HiH RRIZI R_KiK R[OiO RS[W[ RWCW[ R_D_O RiDiQ RjQfU R]RaT ReXl[ RbX[Z RZPRR',
	    'Po]DkD R\\IlI RRIZI R^OjO R^SjS R^VjV RS[V[ RWCW[ R^K^V RdDdH R^KjKjV RfXjZ R`X[[ RZPRR',
	    'PoZElE R]HiH RRHYH R]KiK R[OlO RS[V[ RVCV[ R]E]O RiEiO ReUj[ R\\UaY R`TZZ RdQfX RZT]U RZRaQ^W RcRkQiVfYa[ RYORP',
	    'PocFjF RRIZI R^NhN Ra[h[ RS[V[ RWCW[ RbUb[ RcCcH RhXh[ RcKcQjPjQ RcSeV R\\IlIjK RiUlZ R_U]Z RZORQ R\\H\\R[Y',
	    'PoRIZI R\\KkK R]OkO R[TlT RcZkZ R]ZeZ RS[V[ RWCW[ R\\E\\K RaEaK RdLdT RfEfK R\\EkEkK RbU\\Z RiWk[ RZORR',
	    'Po[FbF RRIYI R^MkM R[RlR R\\ZjZ RR[V[ RVCV[ R\\U\\Z RdMdZ RjTj[ R]G^J R^L[O RcGcJ RjFhK RYPRR RjD`E',
	    'PoRIZI R_KeK R_OjO RZUlU RS[V[ RVCV[ R_K_U ReOeU R\\FkGkJ RfXjY R_YZ[ RcCdF RZORR R\\G\\J RhJ`J',
	    'PoRIYI R\\F\\I R\\FkFkI RfIiK RcCdF RbP^T ReMlQ R_J\\L RcK`N[Q R_SiSeW^Z RZPRQ RVCV[S[ RaVdX[[',
	    'PobJlJ RRIYI RcOkO RcTkT RcZlZ RVCV[ RdHd[ RhIhZ RhDiH R[L_U R^P[X ReCaL RYORQ RR[V[R[ RZG_G]T',
	    'PoRIYI RiLlL RZPbP RS[V[ RVCV[ R^L^[ ReDeK ReDjDjL RlJlL RgVl[ R]PbU RaLaN R^GaJ RiUc[ R_GZL ReQhX R[E_G RdOkOgW R`C^I R^OZX RYORQ',
	    'Po[FkF RRIZI R[KlK R^QkQ R^UjU R\\ZlZ RS[V[ RVCV[ RdMdZ RcCcF R`G`J R_N]S RgGfK RYPRR R[K[RZZ',
	    'PoRIZI RaJkJ RaTkT RaZkZ R\\F\\I R^L^[ RaOa[ ReKeO RaOkOk[ R\\FlFlI RcCdF R_I[R RZPRR RVCV[S[',
	    'PoRIZI RS[V[ RWCW[ R[DbDbM RdDkDkM RgFdH RiHeL R`I\\L R^F\\H RdNlR RcNaOZR RcQ\\T RfT^W RZORQ RiVdY\\[',
	    'Po[FjF RRIZI R\\IjI RZMlM R]VbV RdVhV R]ZhZ RS[V[ RWCW[ R]R][ RdRhRi[ RfMlS R_OZT ReIhP RbH]Q RbP]R RbC_O RZPRQ',
	    'PoRIYI RfSkS RS[V[ RVCV[ R^L^[ RcWfZ RcGeJ RhHkK RbScX R^C\\I R_I[P RiEfL RcDaL RYORQ RbP`Z RgNfZk[',
	    'PoRIZI R^JjJ R^QcQ R\\YlY RS[W[ RWCW[ R\\D\\[ R^L^R R^LcLcQ RjTjW R\\DlDl[ RgGjH RgQaW RiLfS RZORQ RcS^T RfFfLgV',
	    'PoRIZI R\\JkJ R^OdO ReOlO ReUlU RS[V[ RWCW[ RiNi[ R\\DkDkJ RjKhP RZORQ RdS]U R\\D\\QZZ RaMaPaT`Y',
	    'PoRIZI RaPkP RaSjS R`VkV R`[l[ RS[V[ RWCW[ RfMfY R[J]M RYQ]Q]X`Z R[E^G R]WYZ RcE_I RbGfLlN RbGiGeL_N RZPRQ',
	    'Po[GkG RRIZI R_RgR RZUlU RS[V[ RWCW[ RcDcG RcSc[ RfOgR RhNlR RhIkL RdKcN R`KcN`R R^I[L RbHaL R^NZR RZORQ',
	    'Po[GlG RRIZI RaKeK RhLlL RfYiY RS[V[ RWCW[ R\\N\\Q R^R^Y RaCaK RcOc[ ReCeK RiCiK R^RiRiY R\\NkNkQ RZORQ R^E]K',
	    'PoRIYI R_OhO R\\SkS R`[c[ RcSc[ R\\K_L RhVkY R^HaI RfKlP R]W[Y RaJ[P RdEhM RdFkFiJ R^C\\I R]FbF_M RYPRR RVCV[S[',
	    'PoZGcG RRIYI R[PbP RS[V[ RVCV[ R[J[P R^C^[ R[JbJbP R]PbU R\\TXX RgRk[ RfSc[ R^O[V RgOiX ReHkHjN RfDdM RYPRR RgMeW',
	    'Po[FlF RRIZI R_OiO R`XhX Rh[l[ RS[W[ RWCW[ R\\K\\[ R`S`Y RdCdF RdKdS R`ShShX R\\KlKl[ R`G`K RhGgJ RZPRQ',
	    'Po_FlF RRIZI R`NkN R`QkQ R^UlU Ra[l[ RS[V[ RVCV[ R`J`Q ReCeX R`JkJkQ R[E^H RZM]M]XZZ RYPRR',
	    'PoRIZI R^RiR R]VkV R[[l[ RS[V[ RWCW[ R\\H\\L R^L^R RdCdH RdRd[ R^LiLiR R\\HlHlL R^D_G RjDhG RZORQ',
	    'Po[FlF RRI[I R]OkO R^WhW RS[W[ Rh[l[ RWCW[ R\\R\\[ R]H]O RdCdF RkHkO R\\RlRl[ RgThX R`IhL RfHcK`L RcOaW RZORQ',
	    'Po[HlH RRIZI R]KiK R]QjQ R]TjT R]WjW RS[V[ RWCW[ R]M]W R^EjEjK R]MjMjW RfXl[ R`XZ[ RZORQ R]E]K RcEcK',
	    'Po\\FlF RRIZI R\\PkP R^UcU Rf[l[ RS[V[ RWCW[ R^R^[ RaHaO RcCcF RfQf[ RfHfO R\\KkKkO RlYl[ RkTeV RZORQ RcZ\\[ R\\F[RZZ',
	    'Po\\HjH RRIZI R\\LjL R[UkU RS[V[ RWCW[ R\\D\\L RcTc[ R\\DjDjL RhWk[ RgQlV RcDcL^P RbS]T RgNbR R_W[[ R\\ObR RZPRQ',
	    'Po[HlH RRIZI R]KjK R\\QkQ R[TlT RS[V[ RWCW[ R\\N\\Q R]E]K RcCcQ R]EjEjK R\\NjNjQ RaQ_W R^Xl[ RZPRQ RhUeYZ[',
	    'PobIhI RRIZI R\\QkQ R`[h[ RS[V[ RWCW[ R\\F\\Q RaUa[ RhXg[ R\\FkFkQ RcCcF RjUkZ RcG_KgM RaSfU R]U[Z RgJdN_P RZPRQ',
	    'PoRIYI R[JaJ R[OaO RcOlO R[YaY Rh[l[ RS[V[ RVCV[ R[F[O R[P[Y R[FaFaO RaWaY RdEdO RhMh[ RlWl[ ReVa[ RhPdY R]C]F RYORQ R`R[T RdEjEiJgT',
	    'PocEjE RRIZI RbKhK R^LdL RcNkN R^VlV RS[V[ Ra[e[ RWCW[ RdCdN R_QeQe[ R`RaT RiRhT R\\HlHkK RZORQ R\\H\\R[Y RiPaQ',
	    'PocEjE RRIZI RcNkN R`TjT R`WjW R]ZlZ RS[V[ RWCW[ R`P`Z RdIdM R`PjPjZ R\\HlHkK RZORQ R\\H\\S[Z RcEcK RfK_K RiJbK',
	    'Po[GlG RRIYI RS[V[ RVCV[ ReUjZ R\\P`S R]J`M RgPkT RfJjL R`O[T RjIfN RbU\\[ RjOfT R`I[N RdCdQgX RcP`X RYPRR RcMbU',
	    'Po^FlF RRIZI R^KjK R^NjN RZQlQ RS[W[ Rf[j[ RWCW[ R^H^N R^HjHjN R`QZW RbU\\Z RfTcZ R_C]H RZORR R]TkTj[',
	    'PoYGaG RSIXI R[L_L RYPaP RR[U[ RUCU[ R^GaL R]DZL RXQRQ R^Z\\Z RfEgUiZdY R\\P[T`T_Z RjDjLjOl[ RkDcEcQb[ RfPeZ',
	    'PoRIZI R]IkI R]OlO R^RkR R^VkV R^ZlZ RS[V[ RWCW[ R]E]I R^M^[ RdCdI ReMeZ RkEkI R^NZR ReKdP RZORQ',
	    'PoRIYI R[IkI R]KbK R^TkT R\\WlW RS[V[ RVCV[ RdJdP RdQd[ R[EkEkI R_Q]U RYPRR RkKfK RfNkN RbN]N R[E[RYZ',
	    'PoUFlF RWIaI RbIkI RXSjS RVWlW R\\[a[ R\\G\\O R`C`F RXQaQa[ RgGgN RgIlN RgIbN R\\IWN R[J`L RUFUSSZ RhOaP',
	    'PoRIYI R[L`L RaQlQ RZQ`Q RS[V[ RVCV[ R[E[L R`C`[ RdTeW RfHjIdO RfDaJ ReFkFiK RXPRQ RdJgMbO RiMi[f[ R[QZZ',
	    'Po[HaH RcIkI RRIYI RcNkN R[ObO Rh[l[ RS[V[ RVCV[ RcDcRjRi[ RcDkDkR RlWl[ R^D_RaW RcZ`[ ReScY RYORQ R^M]V',
	    'Po\\FkF RRIZI R\\KkK R]QiQ R]TiT Rf[l[ RS[V[ RWCW[ R]M]T RfTf[ R]MiMiT RlXl[ RcCcF R`G`J RfGeJ RaT_YZ[ RZPRR',
	    'Po\\GkG RRIZI R\\MlM RaShS RS[V[ RWCW[ RaIaS RbWb[ RcCcG RhIhS RfVg[ RiVl[ R_V][ RZORQ R\\G\\S[Y',
	    'PoSE_E RSG^G RRJ_J RYP\\P RTTjT RRWlW RZ[_[ RYCYJ RVL]L\\P RUR_Q_[ RWLRP RbFdJgMlO RWJUO RcCaH RbGjFhJeM_O RgP`Q',
	    'PoSE^E RRH_H RSK_K RSM^M RiOlO RTTjT RRWlW RZ[_[ RYCYH RYKYP RTQ`Q_[ R`FiFiO RlLlO RcK_O RVIVK RaIgL R[I[K ReCcL RdP_Q RgPaQ',
	    'PoTE^E RRH_H RTL^L RUO^O RSO\\O RTTjT RRWlW RZ[_[ RYCYH RYJYO RUQ`Q_[ R`FiFjN RaJfL R[I_K RVIRK RlMkPlO RdCcM ReP_Q RhPaQ',
	    'Po_GgG RRIZI R_JgJ R[QkQ R[M[Q R]D]K R]DiDiK R[MkMkQ R`M`Q RfMfQ R_UaXeYl[ RZNRP RVCV[S[ R\\TjTgWcY[[',
	    'PoZFcF RRIYI R[NbN R[SbS RZVcV RS[V[ RVCV[ R[J[S R^C^[ R[JbJbS ReLkLj[ RYPRR RlEeFeSdZ',
	    'Po[DlD RRIYI RaMhM R^VcV RfVkV R[ZlZ R[D[[ R^P^V RaGaM R^PcPcV RfPfV RaGhGhM RfPkPkV RYORQ RVCV[S[',
	    'Po\\DkD RRIZI R^JaJ RfJiJ R]MaM RfMjM R]PjP R[SlS RS[V[ Rc[h[ RWCW[ R[H[K RcDcN R[HlHlK R`S^WiVh[ RZORQ',
	    'Po[GlG RRIXI R`LdL R\\PdP R]Z`Z R`I`Z RhDjF RfWcZ RcSdV RjOeY R[G\\T\\X RXPRR RjLgV RVCU[R[ RlWk[i[ RfCfMfOhY R[PY[',
	    'Po[FlF RRIZI R]KkK R]NkN R[RkR R[UlU RS[V[ Rc[g[ RWCW[ R]H]N RdCdQ RgSg[ R]HkHkN R^VaY RZORQ',
	    'PoRIZI R]MjM R]QjQ R[TlT RS[W[ RWCW[ R]J]Q RcJc[ R]JjJjQ R\\F_H RbFdH RhFkH RjChF R^C\\F RdCbF RcShXlZ RcS_XZZ RZPRR',
	    'Po[FlF RRIYI R\\MjM R\\QjQ RZUlU RR[V[ RVCV[ R\\J\\Q R`C`H RgCgH R\\JjJjQ RbTfYl[ RbV_YZ[ RcQaX RZPRR',
	    'PoSElE RVKhK RVNhN RRQlQ RXUfU RTXjX RZ[_[ RVHVN RZCZG R_S_[ RdCdG RVHhHhN ReRlU R\\NXSRU RbR[S',
	    'PoRIZI R]UkU R]ZkZ RS[W[ RWCW[ R]Q][ R]QkQk[ ReDlDlN RhFeI RjIfM R[DbDcNcQ R_F\\I R`J\\N RZORQ',
	    'Po\\HjH RRIYI RZMaM RVCV[ RfEjH RcKgOlQ RhIfN R]I[M R`C_H RYORQ RbK^PYR RcQ]S RfS]W RS[V[S[ RhVcY\\[ RiKkMeM',
	    'Po[DbD RRIYI R[LbL R[QbQ R[UjU RZZlZ RS[V[ RVCV[ R[D[Q R_L_Q R[IbIbM RcRcZ RhLlQ RhLdQ ReFiO RdEkEgO R_E_I RYPRQ',
	    'Po[ElE RRIZI R\\NkN R]QjQ R[TlT RS[V[ R_[c[ RWCW[ R\\H\\N RaEaN RcTc[ RfEfN R\\HkHkN RhWlZ R^W[Z RZORQ',
	    'PoRIYI RcJjJ R[J`J RbTkT RbZlZ RZ[^[ RS[V[ RVCV[ R[E`D`J RbNbT RcDcJ RgJgZ RcDjDjJ RbNkNkT RjVk[ RYPRR R[J[Q`Q_Z',
	    'PoRIZI R]KjK R[PkP R^ViV R^ZiZ RS[V[ RVCV[ R^S^[ R^SiSi[ RcHdK R_L_O RdCgGlI RgLfP RcC_H[I RZORQ',
	    'PoSE^E RTH^H RhIlI RTN]N RTTjT RSWlW RZ[_[ RYCYH RTJ]J]N RUR_R_[ RbDhDhH RaJdMjP RYJYN RbDaH R`JjJgMdO^P RTJSQ RfP`Q',
	    'Po\\FlF RRIZI R_IfI R_OiO R]TkT R[WlW RS[V[ RWCW[ R\\K\\O RcQc[ R\\KlKlO RgMiP RgH_P R_R\\U RcDeE`I RZPRR',
	    'Po]HjH RRIZI R]KjK R_NiN R^ZiZ RS[V[ RVCV[ R]D]K R^U^[ RdDdK R^UiUi[ R]DjDjK R`L[Q R_PeSlT RZORQ RhOcR[T',
	    'PoRE^E RhHlH RSJ]J RSL]L RRN_N RVRbR RTTjT RRWlW RZ[_[ RSGSL RXCXP RSG]G]L R_Q_[ RbDaH RbDhDhH RbKeMjO RaJjJgMaO ReQ_Q',
	    'PoRIZI R[TlT RS[V[ RVCV[ R[D[M R[DbDbM RdDdL RdDkDkL R_NbO RhNkQ RaQ_V RePcP R^Wk[ RYPRR R\\PZP RgUdY[[ R_G^O RhGgO',
	    'Po[ElE RRIYI R\\KdK R]TcT RS[V[ RVCV[ R]N]U R]G^J RcNcU RfLkLjP RhKhTkZ RfWb[ RbGbJ R`K`U][ RYORQ RgQeX RfGeQ R[E[SYZ R`Q_Y RgOfW',
	    'PobGlG RTTjT RRWlW RZ[_[ RTGTO RVJVM RYCYO R\\J\\M RTG^G^O RUR\\R`Q_[ RTDVF R^D\\F RcHgMlO RcCaI RiHeL`N RgP`Q',
	    'PoRIXI R[K[[ R_C_[ RgRk[ RaEcI RiReZ RXORQ RfMiY RVCV[S[ R\\EZJcKc[a[ RaNaU RfJhW R\\N\\U RfCdO RfIkIhV',
	    'PoRIZI RS[V[ Rd[i[ RWCW[ R\\N\\Q R\\NlNlQ R_HbK RhHlL RkEjH R\\E\\H RbEbH ReEeH RfKdL RbV_Y[[ RZORQ R`C^J RcPaW RhCgJ R\\SjSi[',
	    'PoRE_E RbGlG RTI^I RSK^K RRN^N RTUjU RRWlW RZ[_[ RYLYP RTGTH]G]I RVPWQ`R_[ RbGcJfNlP RcCaI RiFhJdM_O RgQ`Q',
	    'Po\\HcH RfHlH RRIZI R\\LcL RfLlL RaSgS RaVgV Ri[l[ RS[W[ RWCW[ R\\E\\[ RaOaX R\\EcEcL RfEfL RfElEl[ RZORQ RaOgOgZeZ',
	    'Po]HcH RfHlH RRIZI R]LcL RfLlL R`OiO RaShS R`WiW Ri[l[ RS[W[ RWCW[ R]D][ R]DcDcL ReOeW RfDfL RfDlDl[ RZORQ',
	    'Po^HjH RRIZI R[KlK R^QjQ R^TjT R^WjW RS[W[ RWCW[ R^E^H R^N^W RdCdK R^EjEjH R^NjNjW RgYl[ RaX[[ RZPRR',
	    'Po[DbD RdDkD RRIYI RdJlJ R]VjV R]ZjZ RS[V[ RVCV[ R]R][ RdFcJ R]RjRj[ RiLiPlOlP RfLcP R^L[P RYPRR R[F[JaJaO_O R_D^M RhDgL',
	    'Po\\FkF R]IjI RRIYI R^OiO R]UiU RZZlZ RS[V[ RWCW[ R[L[O R]Q]U RcCcI R]QiQiU R[KkLkO R`W`Y RfWeZ RYPRR',
	    'Po[HlH RRIZI R]KiK R[NbN RdRkR RZUlU RS[V[ Rc[g[ RWCW[ R^N^R RdMdR RgSg[ R]DiDiK RdMkMkR R^VaY RZPRQ RbQZR',
	    'PoRIZI R_OiO R^QkQ R[UlU RS[W[ Rd[h[ RWCW[ R\\H\\K R_K_O RdCdG RhQh[ R_KiKiO R\\HlHlK R^D_G RiDhG R^Q^U RZORQ RhUaY[Z',
	    'PoRIZI R^MhM R[SkS R[VlV RS[V[ R_[c[ RVCV[ R[G[J RcCcG R\\PdPc[ R^JhJhM R[GkGkJ R]D_F RiDgF R^J^M RZORQ RiObP',
	    'PoZGbG RRIYI RYLdL R[RaR R[VaV R_[a[ RR[V[ RVCV[ R[N[[ R\\D\\K RaDaK R[NbNb[ RhWl[ RfWc[ RfQjZ RiQeY ReLhX RfDcN RYPRQ ReJgV ReIkIgV',
	    'Po]FjF RRIZI R[JkJ R\\MbM RdMkM RdQlQ R[TlT Rf[l[ RS[V[ RWCW[ R_K_Q RcCcJ RfTf[ RhKhQ RaT_YZ[ RlYl[ RZORQ RbQ[R',
	    'Po\\GiG RRIZI R\\KiK RZNlN R]UiU RS[V[ R`[c[ RVCV[ R\\D\\J R]Q]U RcUc[ R]QiQiU R\\DjDjJ RhWk[ R^WZ[ RZPRR',
	    'PoYGcG RRIYI R\\LaL R\\PaP RYTcT R\\C\\T RaCaT ReLkLj[ RaVbY R[VZ[ RYORQ RVCV[S[ RkDeFeSc[',
	    'Po[DcD R\\NbN R\\SbS RVCV[ R\\J\\X R^D^I RRJcJb[ RgWc[ RfQjZ RiQeY ReMhX RZORQ RS[V[S[ ReIgV ReJkIhV RfCdO RaWYX',
	    'Po\\FlF RRIZI R[JlJ R]RjR R]VjV RS[V[ RVCV[ R]N]V R`C`J RcJcV RgCgJ R]NjNjV RfXkZ RaX[[ RYPRR',
	    'Po]FcF RdFlF RRIYI R_PhP R^ZiZ RS[V[ RWCW[ R^S^[ R^SiSi[ R]E`H RdJjP RgFjI RcJ\\Q R^C[I ReCcH RZORQ',
	    'PoRJ[J ReNjN R]PjP R]SjS RZUlU RS[V[ Rc[f[ RWCW[ R]K]S RaGaM R_GgGfM RgSg[ R]KjKjS R^WaY RaDgDlI R^F[I RZORQ',
	    'PoRIYI RcJlJ R`W`[ RhEkG R[K_OZU RePaT RdVe[ RjWk[ R^DZM R\\IaJ^R R[WY[ RYORQ RfCfJiR R]FbF_O RVCV[S[ RfGeQ',
	    'Po[HkH RRIZI RdQlQ RbWlW RS[V[ RWCW[ RcCcN RdQdW RiNi[ R\\S_V R]D_F RiDgG R`V[[ RcGgKlM R^N[T R]QbQ_Y RcG_K[M RZORQ',
	    'PoRIZI R_KgK R^TiT R[ZlZ RS[V[ RVCV[ R^O^T R^OiOiT RfHlM RiDfG RkGhJ R\\G_I[N R`U`Y RdCgJ RgUeZ R\\EaD^K RYPRR',
	    'Po^EdE RRIZI Rf[k[ RbVbZ RbFcI RhUjW RhEkH ReUfY R]KhKgO R^V][ RZORQ RWCW[S[ R]E\\K]Q[X R]OjOiS R\\SlSk[ RhC`D',
	    'Po\\FkF RRIZI R[JlJ R]PjP R]TjT R\\WkW R[[l[ RS[V[ RWCW[ R]M]T RdCcF RdMd[ R]MjMjT R`G`K RhGgJ RZORQ',
	    'Po[HkH RRIZI R_OgO R`XgX Rg[k[ RS[V[ RWCW[ R\\Q\\[ R_J_O R`T`Z R`TgTgX R^LhKhO R\\QkQk[ ReHjM RaH[N R\\EcE_L RZORQ RhDaE',
	    'PoZGcG RRIYI R[NbN R[RbR RYVcV RS[V[ RVCV[ R[J[R R^C^J R^R^[ R[JbJbR RhDlH RhTb[ RkMgV RYORQ RlWk[ RkKdK RfCfMhY',
	    'Po[HjH RRIZI RS[V[ R^[b[ RWCW[ RbQb[ RZN`M_O RfEjI RhSkX RcKhQlR RhJfO R\\J[N RaC_H R]S[X RcK]QYS ReSeX RZPRQ RiLkNeN',
	    'Po[DlD RRIZI R\\KkK R]QjQ R]TjT R[WlW RS[V[ RWCW[ R\\G\\K R]N]T RaDaK RdTd[ RfDfK R]NjNjT R\\GkGkK RZPRR',
	    'Po\\IbI ReIkI RRIZI R\\OjO R\\SjS RZWlW RS[V[ RWCW[ R\\D\\I R\\L\\S R\\DbDbI RcLc[ ReDeI R\\LjLjS ReDkDkI RZORQ',
	    'PoYGcG RRIYI RYLbL R[SbS R[WbW RS[V[ RVCV[ R[O[[ RaIbM R^C^F[L RYORQ R[ObOb[_[ RfCdN ReIkIhTd[ ReJgTk[',
	    'PoRIYI R[Q`Q RcTjT RS[V[ RVCV[ RZM`L`Q R[G^I ReGjL RhDfG RjHgI R`GZM RdDfI RcUgYl[ R\\DaD_I RYORQ RiUfYb[ RdLiLiQkPkQ R\\[_[\\[ R[Q[U`U_[ RcLcR',
	    'Po[FlF RRIZI R]KgK R[OlO R^WjW R^ZjZ RS[V[ RWCW[ R^S^[ RbHbO R^SjSj[ RbE\\K RePhQ RfFhIlK RZORQ RhKcPYU',
	    'PoRIYI RcRkR Rc[k[ R^U^Z R`TaY RcJcZ RhKhR RbKkKkS RlXk[ R\\N[R ReGiGgM R^CZL R`H\\P RfCbL RYORQ R[UZ[ RVCV[S[ R_KZL R`NaRZR',
	    'Po]HkH RRIZI R]LjL R]OjO R]TlT RS[V[ RWCW[ R^G^S RaWa[ ReFeS ReVgZ RiVl[ R_C\\K ReDdI R]V[[ RZORQ',
	    'Po[GlG RRIYI R]PiP R]TiT RR[V[ R`[c[ RVCV[ R^L^T RcTc[ R\\MiLiT RgVkZ R\\I^K RdHjM RkIhK R^VZZ RaH[N RcC_K RYPRR',
	    'Po`FjF R]GgG R[IlI RRIYI R^MhM R\\SjS R\\VjV RZXlX RS[V[ RVCV[ R\\O\\V RcKc[ R\\OjOjV RgDjG RaC]G RfJlM RbG^KZM RYPRQ',
	    'PoRIZI RZNlN R[TlT RS[V[ RWCW[ R^E^T RaGaT RaWa[ ReGeT R]HjGiT RjWk[ ReWf[ R^C[J R]W[[ RZORQ',
	    'PoRIYI R][b[ Rf[l[ RVCV[ R]P][ RgPg[ RaEaMjLjN RZORQ R`OYQ RjPbQ RS[V[S[ RfD[E RaSZT RjScT RhF[H RiI]K RbVYW RlVbW',
	    'PoRIZI R[KkK R]VjV R]ZjZ RS[V[ RWCW[ R]R][ R\\EcEcP RcRcZ R]RjRj[ RcJhOlQ R^G_I RhFfK RZORQ RcJ]PZQ RiC`E',
	    'Po]HjH RRIZI R]KjK RZNlN R\\RbR R\\UbU RVCV[ R\\N\\Y R]D]K RbNb[ R]DjDjK RfVk[ RiVd[ ReRgX RdQkQhX RZORQ RS[V[S[ RbXZY',
	    'Po\\GkG RRIZI R]JjJ R[MlM R_TjT R_WjW R_ZjZ RS[V[ RWCW[ R_O_[ R^QjQj[ RaMZT R`D`F RcG^Q RgCeG RYPRQ',
	    'Po\\HbH ReHkH RRIYI R\\LbL ReLkL R[QkQ RZVlV R]D]L R`N`U R\\DbDbH RfDfL RgNgU ReDkDkH RfXkZ R`XZ[ RYPRR RVCV[S[',
	    'PoaFlF RdIkI RRIYI RbMlM RcSkS RcWkW Rh[k[ RS[V[ RVCV[ R[D[[ RcPc[ RcPkPk[ ReDaL R^K`R RgIgM R[E_D^L RYORQ R_U]U',
	    'PoRIZI R\\IlI R\\NkN R\\QjQ R[UlU RS[V[ RWCW[ RaCaI RfCfI R`J`M R]D^H RcNcUgYl[ RgJfN RjDhH RcU`YZ[ RZORQ',
	    'PoRIXI R[KaK RZObO RR[V[ RVCV[ R^K^Y R^CbH RdWa[ R\\GYJ RZR[W RhSkZ RgHfRcY R]C[H RXPRQ RgOiY RdHkHjN ReDbN RgMhW RbXYY RaRaV RgMfV',
	    'PoZGcG RRIXI R[MbM R[[^[ R[I[M R_S_[ R[IbIbM R^C_G RZQbP^S RgVb[ RfRjZl[ RiPeY RXPRR RfDcO RVCV[S[ ReJhW ReJkIhV RcUYV',
	    'Po\\EbE ReEkE R[HbH RdHlH RRIYI R\\OkO R]TjT R[WlW R^JbK RdQd[ RgHlM R^J[M RhHdM R`M^T RYPRR RVCV[S[ R_D^K RhDgK',
	    'PoZFlF RRIYI R[JkJ RZNlN R[SkS RYWlW RS[V[ RVCV[ R^J^S R`C`H RcJc[ RgCgH RiJiS RYPRR',
	    'PocElE RZGbG RRIXI RZMaM RZXaX RS[U[ RVCV[ RZQZZ R^C^M RZQaQaX RdIdT RdIkIkT RiWl[ RhEfI RYORQ ReZb[ RhLgSfY',
	    'PoRIYI R\\KkK R\\OkO R[QlQ R]WjW R]ZjZ RS[V[ RVCV[ R]G]N R]S][ RdHdN R]SjSj[ R[IkHkN R`C[I R_EgEdH RYPRR',
	    'PoRIZI R^NiN R^RiR Rf[l[ RS[V[ RWCW[ R[H[K RbCcF R^K^UhUf[ R^KiKiU R[HkHlK RlXl[ RiChG R]D^G RaU_Y[[ RZPRQ',
	    'PoRIZI R]PjP R\\WlW R[F[H R\\S\\X RdJd[ R]LjLjP R\\SlSlX R[FlFlI RbDeF RfHlK RbH[K R]L]P RZPRR RVCV[S[',
	    'PoaFkF RRIYI R_JlJ R`NkN RaQkQ R`UlU Ra[l[ RVCV[ RfCfJ RfNfX R[K^M R[E^G RZQ^Q^XZZ RcKcM R]XbZ RiKhM RYORQ RS[V[S[',
	    'Po\\GkG RRIZI R^JcJ R^QcQ RS[V[ RWCW[ R^L^Q R^LcLcQ R`V`[gZg[ RkQkT RhVlZ RgDjE RiNdS R`TdU RjJgP R\\VZ[ RZORQ ReEgR R\\G\\N[T',
	    'PoRIYI R[JaJ R[MaM RYQcQ R^[`[ RS[V[ RVCV[ R[F[M R[FaFbM RhWl[ RgWb[ RfQjY RiQeY R]C]F RYORQ ReJgU ReIkIhU R^M]Q[Y ReCcN R\\TaTa[',
	    'PoeHiH RRIYI RcUhU Ra[l[ RS[V[ RVCV[ R`M`X RcPcV ReHeM RcPhPhU RbDiDiM RZK]M R[E^G RYQ]Q]WY[ RYORQ RbDaMkMkXhX',
	    'PoRIZI R_JgJ R]SbS ReSjS RS[V[ RWCW[ R]M]S R]MbMbS ReMeS ReMjMjS RiXk[ RfWjY R_WaY RdCiIlJ R^W[[ RfWb[ R^I[J RcC]J RgTeZ R_T]Z RZORQ',
	    'PoYGbG RRIXI RdMkM RZNaN RZRaR RcRkR RXVcV RZJZR R^C^J R^R^[ RZJaJaR RgMg[ RhDkL RhCdL RYQRR RVCU[S[',
	    'PoZFlF RRIYI RcLlL RcPkP RcUkU RcZlZ RcCcF RdJd[ RhJhZ R[M^O R]H[M R`K[T RYORQ RVCV[S[ RdHcO R`S[T RhHhN R`R^WZ[',
	    'Po\\DkD RRIZI R^KaK RfKjK R]NaN RfNkN R]VkV R]ZkZ RS[V[ RVCV[ R[H[K R]R][ RdRdZ RdDdO R]RkRk[ R[HlHlK RZPRQ',
	    'Po\\IjI RRIYI R\\LjL R[OkO R^QiQ R[UlU RS[V[ RVCV[ R\\F\\L R_T_[ R`C`L ReCeL R\\FjFjL RdUhYl[ RjVfW RYPRR RdY^Z R[O[TZY',
	    'PocEkE RRIZI R^KhK RcMlM R]RlR R_TjT Rf[j[ RS[V[ RWCW[ R_O_T RdCdM R_OjOjT R\\HkHkK R]WkWj[ RZPRQ RbZ][ ReOeTcY R\\H\\SZZ',
	    'Po[FlF RRIZI R`MgM R]PjP R^UiU R^XiX RZZlZ RS[V[ RWCW[ R]H]O R^R^X RcCcF R`K`LgKfM R^RiRiX R]HjHjO RZORQ',
	    'Po[FlF RRIYI R]KhK R[OlO R_VjV R_ZjZ RS[V[ RWCW[ R`R`[ R`C`H RbHbO RgCgH R^SjSj[ RkIeObQZU RYPRR',
	    'PoRIZI R\\IkI R]LjL R[PlP R\\TlT R\\WkW RS[V[ RWCW[ R\\D\\I RaDaI RdIdP RdTd[ RfDfI R\\DkDkI R`Q`T RhPgT RZORQ',
	    'Po[IlI RRIYI R[MlM R\\QkQ RZTlT RS[V[ RVCV[ RaCaH RcMc[ ReCeH R\\D]G RkDiG RhIgL R_J_M RcShXlZ RcS_XZZ RZORQ',
	    'PoRIZI R\\IlI R]UgU R^YhY R[YdY RS[W[ Rg[j[ RWCW[ R\\D\\I R]P]T RaDaI RbNbY RfDfI R]PgPgT R\\DlDlI RfVhY R_I^MZPRQ R]MlMlUk[',
	    'PoRE^E RhHkH RSK]K RRM^M RTP]P RURbR RTTjT RRWlW RZ[_[ RXCXO RSG]G]K R_Q_[ RbDhDhH RZI]I RcMgOlP RSGTJWI RbDaH RXIUI R`JjJgMdN_O RTNTP ReQ_Q',
	    'PoRI[I R_PiP R]VkV R]ZkZ RS[W[ RWCW[ R\\H\\K R]R][ R_K_O RdCdH RdRdZ R_KjKjO R]RkRk[ R\\HlHlK R^D_G RjDhG RZORQ',
	    'Po]FcF ReFlF RRI[I R[LlL RS[V[ RWCW[ R]Q`T RhQlT R_F`I RhFiI ReVhYl[ RiNfU R^C[I R^N[U ReCcH RbU_YZ[ RZPRQ RcJcPdV RcNbV',
	    'PoRIYI R_IhI R[QaQ ReQkQ R[TlT R[L[Q R_D_I R[LaLbQ RcRc[ ReLeQ R_DhDhI ReLkLkQ RcSgXlZ RYPRR RcS_WYZ RVCV[S[',
	    'PoRE_E RbGlG RTMZM RUQbQ RTTjT RRWlW R[[_[ RTJTN RTJZJZM R[C[F R_Q_[ RTGSK RbHgMlO RcC`J RjGfL_N R]OZO RTI^I]O RdQ_Q RgPaQ',
	    'Po`FdF RRIYI R\\P\\W R]C]H R`C`I RgWd[ RgRkZ RiQfZ R\\IZP RfNiX RZF]FZF RXORQ RVCV[S[ Ra[^[ RfJhV RfDdO R\\P`P`U\\V RfJkJiV R\\MbLbVa[',
	    'Po\\IkI RRIZI R[KlK R]SjS RS[V[ RWCW[ R\\D\\I R]N]S R`U`[ RaDaI RfDfI R]NjNjS R\\DkDkI RdSeVhYl[ RdSZW RkUfW RZORQ ReZ\\[',
	    'Po[IbI RRIYI R[PbP R[SbS R[WcW RYX`X RS[V[ RVCV[ R[D[I R[L[W RYLcLb[ R[DbDbI RhEkH RgWc[ RjOfY RYORQ RlLcM RgCfNhZ RlWj[k[',
	    'PoRIZI R^IhI R]QjQ R^XhX RS[V[ Rg[k[ RWCW[ R\\S\\[ R]K]Q RcGcJ RjKjQ R\\SkSk[ ReUiX R`LgN RdCgGlI RcC^HZJ ReL_O RbQ`X RZORQ',
	    'Po[EkE RRIXI RbKkK RcNkN RcRkR RcWkW R[ZlZ RR[V[ RVCV[ R[E[Z RcIcY RhKhW R]M_O R]H_J RgGhI RcKaM R`S]X RYORQ RcGcO',
	    'Po\\IlI RRIZI RfLkL RaNiN R]PlP R`RiR R`UiU R_ZjZ RS[W[ RWCW[ R_W_[ RfIfL R_WjWj[ R_FfEeI RbIaL^M RZPRQ R`E\\I]Q[Y',
	    'Po]GlG RRIZI R^JkJ R^MkM R^PlP RaWgW RS[W[ Rh[l[ RWCW[ R\\S\\[ R^F^P R\\SaSaW ReEeP RgSgW RgSlSl[ R^G[J R_C\\I RZORQ',
	    'Po[FlF RRIZI R]LjL R]OjO RS[V[ R]YgZj[ RWCW[ R]I]O R^S^Y R`C`G RgCgG R]IjIjO RcTfX RaO[T RcS_W RZPRR R^RkSj[',
	    'PocGlG RRIYI R[LaL RbNlN RcTlT R\\YaY R[E\\R\\[ R[EaEaL R\\QaQaY RdIeM RgDgG RgNg[ RjHiM RYORQ RVCV[S[ R[NYY',
	    'Po`FkF RTH]H R_JlJ R`MkM RUO]O RTTjT RRWlW RZ[_[ RUKUO RTD]E]H RUK]K]O RVR`Q_[ ReCeF ReJeO RbGbI RhGhI RTFSN RgPaQ',
	    'PoRIZI R^LiL R[UlU RS[V[ RWCW[ R\\F\\R RcHcQ R\\FjFjR R_HaJ ReMgP RbL`P RgHfJ RbDaF RdUgYl[ RcSaW_YZ[ RYPRR',
	    'PocEjE RRIZI RbKhK R^KeK RbMjM R^PkP RVCV[ RcIcM RcCcH RbRfUlY RjSgT RePhW R\\HkHkK RfP^T RcT^V RdUbX]Z RZORQ RS[V[S[ RdSfWe[b[ R\\H\\SZY',
	    'Po[FlF RRIZI R[KlK RcNhN R^QkQ R[TlT RS[V[ RWCW[ R^H^Q R`C`H RcHcN RdRd[ RgChHhN RdThXlZ RdT`X[Z RZPRR',
	    'PoZFdF RRIYI ReKkK R\\UbU R`[c[ RR[V[ RVCV[ RZKZ[ R\\Q`P_Y R_C_J RZKcKcZ R]L]O RjEkH R`L_S RYORQ RhChRe[ RhKkZ RhKfY',
	    'PodHkH RRIYI R[IaI RdMkM R[NaN RgQkQ RYZlZ RS[V[ RVCV[ R[D[O R\\S\\Z RaSaZ R[DaDaN ReSeZ R\\SjSjZ RdDkDkQ RYPRR RdDdKcP',
	    'PoYIbI RRIXI R^C^N RhWl[ R]IaL RZE[G RfWb[ RbE`G R^IYN RfQiY RZUbY RhQeY R\\OZU RXORQ RYRaR_V\\YX[ RUCU[S[ RfCdLfV ReIkIgV',
	    'PoZElE RRIYI Rf[j[ RS[V[ RVCV[ R[K[[ RbEb[ R[KjKj[ R]T`W ReNgQ R_N\\Q RgNdQ ReTgVdX R^FZJ R]O`P RfGlJ R_S^W RYPRQ',
	    'Po[FlF RRIZI R]WiW RS[V[ RWCW[ RcCcF RcJcO RfJfO R^RiQi[ ReGeI RiIlO R]J[O RYORQ RkIfJ R`O^O RiNeN R]R]Y R[JaHaI`O',
	    'Po[ElE R]HjH RRIZI R]MjM R]RjR RgSlU R\\UkU R]WjW RZ[l[ RS[V[ RWCW[ R[O[R R]J]M RcCcH RcUc[ R]JjJjM R[OlOlR RaR^U RZORQ',
	    'PoRIZI R]QkQ R]SjS R]WjW R]ZlZ R^O^[ R[DbDbM RdOdZ RdDkDkM RgFdH R]PZT R^G[H R_I\\M R^M\\R RhJdK RZORQ RWCW[S[',
	    'Po[EkE R\\HjH RRIZI R]MiM R\\PjP R[RlR RcUlU R\\YaY RS[V[ RWCW[ R\\T\\Z R\\TaTaY RcCcH RcMcO R[KlKkM RdWfY RZORQ RiSi[f[',
	    'Po]GjG RRIZI R]JjJ R]LjL R\\QkQ R\\SkS RZVlV RS[V[ RWCW[ R\\N\\S R]E]L RcNcS RgVg[ R]EjEjL R\\NkNkS RcCcE R`V_YZ[ RZORQ',
	    'PoSE^E RaFlF RbHjH RSK]K RaNkN RSO^O RUQbQ RTTjT RRWlW RZ[_[ RSESO RXEYH RXKXO RSH]H]K R_Q_[ RaJkKkO RbE_H RdKdO RhKhO RaKaO RdQ_Q RgPaQ',
	    'PoRIYI R`PgP R\\TkT R`[c[ RVCV[ R[F[I RcCcF RcTc[ R[FkFkI R\\M^P RhWkZ RfLkR R]KaM\\R R_W[Z RfJkJgN R^IZN RdHgN R^JbJ`O RZPRR RS[V[S[',
	    'Po]EbE RVGZG RdGhG RVJZJ RdJhJ RRMlM RWTgT RSWkW RZ[_[ RVEVM RYQ_Q_[ RaHaM RdDiDiM RdMhQlS RZMURRS RZCUD R]J]N R]C]HaH RcP[Q',
	    'Po_HcH RRIXI R]NbN R_QcQ RR[U[ RUCU[ R`F`Q R^SbSbZ RgHlHjM RiTl[ RhKhSd[ R\\KcKcM RXORQ RgCeM RhNfX RhMjX RcDZEZSX[ R\\K\\TZ[ R^S][',
	    'Po\\DkD RRIZI R]JbJ RfJjJ R]MbM RfMjM R[PlP R[G[J R\\S\\[ RaSa[ RcPbS RdDdN RfSf[ R[GlGlJ RZORQ RWCW[S[ R\\SkSk[i[',
	    'PoTDlD RaJlJ R]Q_Q RXUjU RVWlW R][a[ RZSbSa[ RhMlP RiFjH RfHiN RdOaP R[H_H R\\O`O RWLWP[O RWM_L_P\\O RfFeN R[HWGXJ[J RTDTRSZ RXF_F_J[J RgRaR',
	    'PoZElE RRIXI R]JcJ R]McM RdMlM Ra[c[ RS[V[ RVCV[ R]P]Z R]G]M R]GcGcM R]PcPcZ RjHkK R^RbS RXORQ RcU^W RhGhPfZ RhKjY RZEZSY[',
	    'PoRIYI R[KbK R[OaO RgSlS RYTbT RS[V[ RVCV[ R[C[J R^UaX ReHiJ RcDkDgI RYORQ R[LZR RaEZG RdQbZ RbMkMkQ R]O]S\\XYZ RcVgZm[ RgMgZ',
	    'Po[FkF RRIZI R]KkK R]MjM R]PjP R]RkR R[UjU RS[V[ RWCW[ R^I^R R`C`G RdIdR RgCgH R^IZM R^VcYk[ RZORQ RiVdY][ ReYZ[',
	    'Po[FdF RRIYI RZJlJ R\\OdO R\\RcR R\\UcU R\\XdX RS[V[ RVCV[ R\\N\\Y R`C`J RaMaX RlWk[ RhEkG RgVb[ RkNfX R]KZQ RYPRR RfCfLhY',
	    'PoRIZI R^RjR R^UjU RS[V[ RWCW[ R\\F\\I RdCdF RdIdM R[PjOjX R\\FkFkI R`K\\L R_O_XhXl[ RZNRQ R^IiJeM[P RaYZ[',
	    'PoRIZI R`MgM R[SlS R[VlV RS[V[ R_[c[ RWCW[ R`IaM RcCcF RdVd[ R[F[GkFkH RiIlL R]J[L RbHeI RaOaS ReOfS R]OjOjS R]O\\S RZORQ',
	    'Po\\HbH RfHlH RRIYI RfLlL RaYgY Rh[l[ RS[V[ RWCW[ R\\E\\[ RaTaZ R\\EbEbL RfEfL R_UhThY RfElEl[ R\\LbL^Q RaOdRiT RaOgOdR^U RZORQ',
	    'PoRIXI R`NdN RZPdP RZSdS RZVdV RZKZS R]H]M R[HaHaN RZKdKdS RfEf[ R]EbDdI R^XdZ R[FYJ RYORQ R]YY[ R_S]Y RVCV[S[ RfElEiMlQlVhV',
	    'Po\\GcG ReGlG RRIYI R\\ObO R\\SbS RgWkW R\\L\\Z R\\LbLbS ReLe[ ReLkLkW R_F`I RhFiJ R]C[I RfCdI RYPRR RVCV[S[ R`UbY[Z',
	    'Po\\FlF RRIZI R\\IlI RaMhM R\\OlO R^UkU RS[V[ RWCW[ RaFaM RdCdF RdOdW RhFhM R^RkRkW R^R^XiXl[ RaY\\Z RZORQ R\\F\\RZZ',
	    'Po[FlF RRIYI R\\IcI RdIkI R]TkT R\\WlW R`[d[ R`G`O RbKbO RcCcF R^ReRd[ RhGhO RkKkO R]K]P ReKdO RYORQ RVCV[S[ R[F[RYZ RiQbQ',
	    'Po\\GcG ReGlG RRIYI RZMlM R\\SbS R\\VbV Rh[k[ R_[b[ RVCV[ R\\P\\[ R\\PbPb[ RfPfW RkOk[ R_J`M R_F`I RgFjI RgJfM R]C[I RfCcI RYORQ RS[V[S[',
	    'PobDkD RZGaG RRIYI RcMjM RZMaM RcQjQ RcVjV R[XaX RS[V[ RVCV[ R[Q[Y R^C^M R[QaQaX RcHcV RgDfH RcHkHkV RiWlZ ReWa[ RYORQ',
	    'PoRIYI R[PjP RS[V[ RVCV[ R[J[P R\\R\\[ RbRb[ R[JjJjP R[F^I RaFdH RgFjI RjCgF R]C[F RcCaF ReSgS ReVgV R^SaS R^V`V RlYl[ RYPRR R`Z[Z RgYbZ R^KgN RgKdL^N RiSj[',
	    'PoYFdF RRIYI R[PbP RZRcR RYUdU RS[V[ RVCV[ R[K[P RaCaI R[KcKcP R\\C\\I`I^VaY R]WZ[ RhKgSc[ RYORQ RgOjZ RfHlHjM RfCdN RhMiX RgMfW',
	    'Po[IkI RRIYI RZNbN R[TaT R[WaW Re[l[ R_[a[ RVCV[ R[D[H R[Q[[ R[QaQb[ ReSe[ R[DkDkH ReJeQkPkQ RlXl[ RaLbO R]J[N R`D`I ReDfI RYORQ RkLeM RS[V[S[ RkUeV',
	    'PoYHdH RRIYI R[KcK R[QcQ RYTdT RS[V[ RVCV[ R[N[Q R[E[K R[EcEcK R[NcNcQ RgWc[ RgRkZ R[WbZ RiQeY R_C_P[W RYORQ RbTaW_YZ[ RfMiX RfJhW RfCdO RfIkIhV',
	    'PoRIZI R]KjK R^WjW R^ZjZ RS[V[ RVCV[ R^S^[ RdHdN R^SjSj[ R\\IjHjN RePfR R^G]NjOlR R]P[R RaD[I R_FeEcI RaPaR RYORQ',
	    'PoRIYI RfPkP RZP_P RZSlS RaFaPdPc[ RbCbFeFeP RgGiL R[G]L R^M_P RjLkQ RcShXlY RjHgO R]D\\I R^H[P RhDgI RXORQ RcS^XYZ RVCV[R[ ReK`K',
	    'Po[DkD RRIZI R]IiI R]LiL R`SgS R_VhV RS[V[ RWCW[ R[N[Q RcDbG R]GiGiN R[NlNlQ RaOdP RhPkS R]Q[S R`PaS_V[W R`WdYk[ R]G]NRQ RgWbZZ[',
	    'Po_FlF RRIYI RbNiN RcUhU RhXkX RaZlZ RVCV[ R`J`W RcQcV ReLeQ ReCeF R`JkJkW R[K]M R[D]H RZQ]Q\\WYZ RbGbJ RhGhJ RYORQ RcQhQhU RS[V[S[',
	    'PoRF[F RbFkF RROkO RXUfU RTWiW RZ[_[ RWCWM R_R_[ RgCgM RVFZJ R]I`L R]D`G RfFlL RgFbK R`D\\G RWGRL RdOgRlT R`H\\MXRRT RbQ\\R',
	    'Po[FkF RRIZI R\\IjI RZLlL R^ShS R^UhU RS[V[ RWCW[ R^P^Z RcFcL R^PhPhU R`NfMlR R^D_E RhDfE R_MYS RbUeYk[ RjVeX RZPRQ RdZ[[',
	    'Po]IjI RRIZI R]MjM R[UbU ReUlU R[ZbZ ReZlZ RS[V[ RWCW[ R[P[[ R]D]M R_P_Z R[PbPb[ RdDdM RePe[ RhPhZ R]DjDjM RePlPl[ RZPRQ',
	    'Po[FlF RRIYI RdJkJ R`PfP R]TjT RS[V[ R_[c[ RVCV[ R`C`H RcTc[ RgCgH R\\O_P RhVkZ RfMkS R^MaM[S R^V[Z RjKgN R^IZM RdIgP R]JbJ_O RYPRR',
	    'PocEjE RRIYI R_KhK RbMkM R_RiR R_UiU Ra[h[ RS[V[ RVCV[ R_O_U RbWb[ RcCcH RdOdU R_OjOjU R[HkHjK RiWlZ R^W][ RbIbM RbVfW RYORQ R[H[SZY',
	    'Po\\FbF RdFkF R\\IbI RdIlI RRIZI R^QjQ R^TjT R^WjW RS[V[ RWCW[ R^N^W R^IaK R^NjNjW RgGiKlM RgJdM R_C_J[N RfYl[ R`YZ[ RZORQ RgEgK',
	    'Po\\EaE RdEkE R[HbH RdHlH RRIXI R\\NkN R]SjS R]VjV RZXlX RS[U[ RVCV[ R]Q]V RdLd[ R]QjQjV R_C^JbK RgHkL RhHeK R]LZM RYORQ RhDgJ',
	    'Po^HiH RRIZI R]KiK R\\NkN R[TkT R_[h[ RWCW[ R[F[H R`V`[ RcCcF RcGcM R[PkPkT R[FlFlH RbUdW RhWlZ R\\W[Z R`PaU RfPfT R[P[T RZORQ RS[V[S[',
	    'Po[ElE RRIYI R[KkK R^McM RePlP R]SgS R\\UlU RS[V[ RVCV[ R^K^P R`E`K RcCcF RcRc[ RfEfP R[HkHkK RcTgXlZ RYORQ RcT`X[Z RkMeM R[E[RY[ RbP\\P RiRcR',
	    'Po]DkD RRIZI R^JbJ RfJjJ RfMkM R]MbM R]RlR R^TkT R^WkW R^ZlZ RS[V[ RWCW[ R\\G\\J R^P^[ RdDdM RePeZ R\\GlGlJ R^Q[T RZORQ',
	    'Po[DlD RRIYI ReKlK RdUkU R[[l[ RS[V[ RVCV[ R_T_Z RaGaQ RdRdZ RhGhQ R]KaKcN RhIjO RhIeO R`J^O RYORQ RcG]G RkGdG R[D[QYZ',
	    'PoZGbG RRIXI RdNlN RZQbQ RdRlR RdVlV RR[U[ RVCV[ RZKZQ R^D^[ RZKbKbQ RdJdV RdJlJlV R^SbV RiXl[ RfXc[ ReFkFiJ R^PZX RfCcJ RXPRR',
	    'PoRIZI R_KiK R^MjM R_RiR R\\ZlZ RS[W[ RWCW[ R\\D\\[ R_G_J R_O_R RbGbK RfGfK R_OiOiR R_GiGiJ R\\DlDl[ RdRfTjW RjSgT RdW`W RaS`W RZORQ RfR^T',
	    'PoYEkE R\\HjH RRIYI R[MiM RZV^V R[[^[ R[J[M R[E\\H RZO^O^Z RgRhV R[JiJiM RfOjOkYl[ R`UcX RYORQ RZR^R RaN`U R_QdQbY RVCV[S[ RZOZUYZ RfOfUeZ',
	    'PobEkE RRIYI R`JgJ R^KdKjL R_QjQ R_SjS R\\ZlZ RS[V[ RVCV[ R^V^Z R_N_S RbVbZ RdNdS RfVfZ R_NjNjS R^VkVkZ R\\GkGkJ RcHcL RYPRR RbCcG R\\G\\SZZ',
	    'PoeFlF R[FcF RRIYI RZLcL ReNkN ReQkQ R\\SbS ReTjT R\\VbV ReWkW R_[b[ RVCV[ R\\O\\[ R\\H\\K R_C_G RaH`K R\\ObOb[ ReCeIkIkM ReNe[lZl[ RYORQ RS[V[S[',
	    'PoRIXI R]IiI R]LiL R[OkO R]SiS RR[U[ RVCV[ R[F[I R_V_[ R[FkFkH RdUiZ RbCdF R`H`O\\S RiUfX RfHfOhQlR RXPRR RbT[W RdZ^Z',
	    'PoaEeE RfHjH RRIZI RfKiK R\\K_K RZNkN R]ShS R[WkW R\\E\\N RcPc[ RaCaIeIeN RfDjDjN RfNkS R_NZT R_D\\E RaKaN R\\H_H\\H RZORQ RVCV[S[',
	    'PodFkF R[FbF RRIYI RiNlN RS[V[ RVCV[ R]P]V R_C_I RdIkIiN R]PjPjV ReXk[ R\\D[G R]I\\M ReDdG RhCgIeM RZIaIaM_M RYPRR RaY[[ RcRbX',
	    'Po]IiI RRIZI R\\KjK RZNlN R^ThT RcWjW Ra[l[ RS[V[ RWCW[ R^P^T RcCcF RcTc[ R[F[GkFkH RgGfNkS R`G`N[S R]WZ[ R]RhQhT RZPRQ R_Zg[',
	    'Po[HbH ReHlH RRIYI R[KbK ReKlK R^NiN R_UiU Ri[l[ R[D[[ R_P_U R[DbDbK RdMdZ ReDeK R_PiPiU ReDlDl[ RdThY RdU_Y RYPRR RVCV[R[',
	    'PoRIXI RZKlK RbReR RS[V[ RVCV[ R^K^Y R]F_H RbFeI RhEkH RgVc[ RjPfX RlWk[ R]C[J RbC`I RYPRQ RjNhW R[N^N[N RbNeNaN R[R^R[R R[U^U[U RbUeUaU RgCgPhZ RbKbYZY RdX\\Y',
	    'Po\\GbG ReGkG RRIZI R\\JbJ ReJkJ R\\MbM RZTlT RS[V[ RWCW[ R\\D\\M R\\DbDbM ReDkDkM ReDeMjNlQ RfNdQ R^N[Q RZORQ RaQ^WkZ RgUcY[[',
	    'Po[ElE RRIYI R[KkK R^NcN RfQlQ R\\VlV RS[V[ RVCV[ R^L^Q RaEaK RdRd[ RfEfK R[HkHkK R^S`T RbCdE RiRgT RdUgXlZ RcU`Y\\[ RfLfP RYORQ RkMeN RcP^P R[E[QYZ',
	    'Po[FkF RRIYI ReLjL R[NkN R\\QjQ RZTkT RVCV[ R`V`Z R\\H\\LaL`T R\\HbHbL RcCcF ReHeL RgLgT ReHjHjL RdUgYl[ RcTZX RjVfW RZORQ RS[V[S[ ReZ\\[',
	    'PoRIZI R]JjJ R^LcL ReOkO R]VjV RS[V[ RVCV[ R^R^V ReJeO R[HjGjJ R\\SjSjV RgXiY R`O[T R_EfEdG ReVe[lZl[ R]F]J RZNRP R`C\\H^K]O`PfQdSbW`YZ[ RjLdM',
	    'PoRIYI R^SiS R\\ZjZ R[L[P R\\U\\[ R^OiOiS R\\UjUj[ R[LlLlP R^HbI RhGkJ RlEjG RcEaG R\\E\\G ReEeH RYPRR RfJdJ R_D^I R^O^S RhCgI RVCV[S[',
	    'PoRIYI ReIjI R\\NaN RdNjN R\\QjQ RVCV[ R\\L\\Q R\\S\\Z RcSc[ RcCcF RdKjKjQ ReSgT ReVgW R^SaT R^V`W ReGeI R`H[J R[FkFkH R[FZH RaK[K RZORQ RS[V[S[ R`YYZ RiSj[ RhZaZ',
	    'Po\\GkG RRIZI R]LjL R]OjO R]QlQ RfVkV R`ZgZ RS[V[ Rh[k[ RWCW[ R\\S\\[ R\\D\\G R^J^Q R`W`[ RdCdG RdHdQ RfSfV R`WgWgZ RkDkG R\\SkSk[ RbS_V R_HZL RZORQ',
	    'Po\\DkD R_GhG RRIZI R_JhJ R\\MkM R\\RaR ReRkR R\\UaU ReUkU RS[V[ RWCW[ R\\O\\Y R_D_L RZObOa[ ReOeY RhDhN RdOkOk[ RZNRP RaXZY RjXcY',
	    'PoRIYI R\\IkI RdOlO RdRkR RZTbT RdVkV RdZlZ RVCV[ R\\E\\I R^S^[ RaEaI RdMd[ RfEfI RiMiZ R\\EkEkI R`QbT R`M^P R[N^P[S R]K\\O RaVaY R[UZZ ReKbQ RYORQ RS[V[S[',
	    'Po[ElE RRIYI R^LcL R]OcO ReOlO R_SbTjU R]ZkZ R[E\\S][ R^J^O R`E`J RdSdY RfEfO R[GkGkJ R]QkQk[ RcUjX ReU_X RYORQ R[JkKeM RVCV[S[ R[OYZ',
	    'Po[FlF RRIYI ReIkI RfSkS R]UbU RfUkU R]XbX RfXlX RS[V[ RVCV[ R`G`O R]SbQb[ RfQf[ RhGhO R\\IaIcM RgIlN RhJcN R_I]N RYPRR R[F[SY[',
	    'PoYFcF ReIlI RRIXI RZNbN ReNlN ReTlT ReZlZ R`[b[ RYRY[ RZHZN RbHbN RYRbRc[ RfGf[ RiIiZ R]I`L R`H[M RiEiH RXPRR R_T`W[V R^N\\W RfCdM RUCU[S[',
	    'Po\\FbF ReFkF RdIlI RRIZI R]QjQ R]TjT R]WjW RS[V[ RWCW[ R]N]W R_C_I RgChIhM R]NjNjW R]D[G ReDdG RgYl[ R]J[N RfIdM RbL_M RaYZ[ R[IaI`M RkMiM RZPRQ',
	    'Po\\DbD R[FcF R\\IbI RdKlK RRKZK R\\KbK R\\PbP RTUjU RRWlW R[[_[ RWKWQ R\\M\\P RVS_R_[ R\\MbMbP RiJiQ RRFUH RdFgH RjFeK RUCTG RXFSK RgCfG RYMYO RkMlP RTMRP RfMdP RaR]R ReQ_R',
	    'PoYFcF RdIlI RRIXI R[IaI RdNlN RZPbP RZSbS RdTlT RYVbV RdZlZ RZLZP R[C[I RaCaI RZLbLbP ReGe[ RiGiZ R^I]WaZ R]WY[ RYORQ ReCdL RVCV[S[ RiDhK',
	    'PodFlF R\\FbF RRIYI R\\KjK R\\MjM R\\OjO RZQlQ R]XiX RR[U[ R`[c[ RVCV[ R\\H\\O R\\HjHjO RgUiX ReQlU R]C[G ReCcG RYORQ RaQYU RbS^U ReT]X R_Y[Z RgYkZ RdWc[',
	    'Po[DbD RdDlD RRIYI R[MlM R[SkS R^WcW RS[V[ RVCV[ R[G[K R^T^[ R`N`S R[GbGbJcM RfNfS R[PkPkS ReGkGkJ RfTf[lZl[ RYORQ RkVeW ReGeK RcZ\\[ R_H^J RhHhJ R[M[TZZ',
	    'PocElE RZFbF RRIXI R[MaM RdMkM R[PaP RdRkR R[SaS RYVbV RdVkV RR[V[ RVCV[ R[J[U R[JaJaU RdIdV RgEfI RdIkIkV R`WbY RiXl[ R[WYZ RfXb[ RXORQ R^D]J',
	    'PoRIYI R]JfJ RR[U[ RVCV[ RcRcY RZFZGkFkH RgLjO R`X`Z RaSaX RiXi[ RbJaOeNfP ReGjH R_G[H RXORQ R]R^Z]Y RfRhZfZ RjQl[ R\\K\\P RjQcR RaQZRY[',
	    'PoRIZI R^KiK R\\QjQ R\\TkT RZWlW RS[V[ RWCW[ R[G[J R^I^L R`Ya[ RcCcG RcNcW R^IiIiL R\\NjNjR R[FlGlJ RiXl[ R^D_F RhCgF R\\YZ[ ReYf[ R\\N\\R RZPRQ R`O`R',
	    'PofGjG RRIZI R]I`I R]QiQ R]TiT R]ViV RS[V[ RWCW[ R[L[P R]O]V R]OiOiV RgDjDjL R]E\\LkLkP Re[lZl[ RbEeEbG ReHbJeJ R`D\\D ReWe[ R`G\\G RfJjJfI RYORQ R_ZZ[',
	    'PoRIZI R\\JbJ RdJjJ R]OjO R]QjQ R]SlS R[ViV RS[V[ RWCW[ R\\D\\J R^M^S R\\DbDbJ RdLdS RdDjDjJ R_H[H R\\FbG^H RhHdH RdFjGfH R^J]MZORQ R^WcYl[ RhWcYZ[ ReDdJcQ',
	    'Po[DbD RdFlF ReGjG RRIYI R[JaJ R[MbM RdMkM R]QiQ R]TiT R]WiW R[D[M R]O]W R^D^G R^J_M R[GaGaJ RdJdM RfJfM RiJiM R]OiOiW RdJkJkM Re[lZl[ ReCcH ReWe[ RYORQ RVCV[S[ RaW^ZY[',
	    'Po^GgG R[IlI RRIYI R[WbW RdWkW RjXk[ RdZkZ RZ[b[ RS[V[ RVCV[ R[T[W R^R^Z R[TbTbW RdTdW RgRgZ RdTkTkW R_C^G RdLeP RbJeMhPlQ R_EhEfI RdI\\L RYPRR RcL\\O RiKdN\\Q RdRaR',
	    'PoZEkE R\\IjI RRIYI RdOiO R[QjQ R\\SjS RZUlU RS[V[ RVCV[ R^W^[ R]M]OaO`U R]MbMbO RcDcK RdMeOfU RdMiMiO R\\G\\HjGiI RZKZLkKkM RcUfYk[ RYORQ RjWfX RbVYX RdZ[[',
	    'PoSHkH RTNhN R_C_N RXP]VcYk[ RgObU\\XR[',
	    'PoRH]H R]ZlZ RWCWM RYUR[ RRN\\NWW RdCdO`W RTPVVZYcZ RdIcS R_IjIjQiWeW',
	    'Po]HlH RRI[I RWCWI RdCdN R_OaTfXl[ R^NjNhScW[[ RTKUT RYKXV R\\URV',
	    'PoRF_F R`HkH RTWYW RY[][ RTQTX RTQYQYW RRN^N][ RfCfN RdTjZ RXH[J^L RbOeW R`NkNhUcY_[ RWJRK RXCXI',
	    'Po^GlG RRH\\H R_JjJ R^LdL RfPlP R]SlS ReYiY R]ZlZ RWCWM RaLaP RfLfP RiQiY R`DjDjJ RfLlLlP RaTcW RYUR[ RRN[NWW RdO]P RTPWVYX\\ZcZ',
	    'Po_GkG R_C_M RXNYR^VcYk[ RTMgMeQaV\\XR[',
	    'PoXIkI RXJRP RgIcQ^VT[ R[CXJ[PaWgYl[',
	    'PoTFTU RZCZ[ R`LdUiYl[ RaD]O RiKeT`Y\\[ R`IcS R`IkIgR RZSRU',
	    'PoRE^E RaIlI RU[Y[ RdUj[ RaMfW RbE]O RiLeTaY\\[ RaIdT RbC_M RiIfS RUESP RTM\\M[RZZ',
	    'Po`IkI RWHW[ R[F[V R`LdUhYl[ RXDSN RaE]O RiKeT`X[[ R`JbS RaC_L RiIfR',
	    'Po`IlI RRM[M RRW[W RSMSW RRF[F[N R[T[W RaE]O R_IdUhYl[ RiIeTaX][',
	    'Po`IlI RRN[N RRX[X RSFSW RRF[F[O RdTjZ R\\T[X RaMeV RaE]O RiKeTaX[[ R`IcS RiIfS',
	    'PoRG\\G RWGWT R]SRU R_IkI RaE\\O R_IcUgYk[ RhIeTaX\\[',
	    'PoRE]E RaIlI RRM]M RXEX[ RdTj[ RaMfW RhMeUaY\\[ RaIdT RbC_N RiIfS',
	    'PoX[[[ RTN\\N\\Y ReUk[ RcNgW RUGRN RiMeUaY][ RWE[E^L RbIeU RbIkIfT RcC`N RWNUY',
	    'PoRH_H RX[\\[ RXCXH RVM]M]W\\[ RbInI RcD_N RaIfTjYnZ RkIgTcX_Z RVHVPUUS[',
	    'PoRF_F RYN^N RTKTX RYFYW R_URX RbInI RcD_N RaIfTjYnZ RkIgTcX_Z',
	    'PoaIlI RRP\\P RRX\\X RRHRZ RRH\\H\\Y RdTjZ RaMfW RWCUH RbE]O RiKeTaX[[ RaIcT RiIgS',
	    'PodHlH RVI]I RRX[X RRORZ RVCVO RRO[O[X RdCdM R`NbTgYl[ R^NjNhTdX\\[',
	    'PoUTZT RX[][ RULUU RULZLZT RfUl[ RfU_[ RcNgX RhNdW RVDRL RbIeU RbIkIfU RcC`N RUH^H^U][',
	    'PoTJ_J RTP_P RTETY RTE_E_J R`W`Y RfUl[ RfU`[ RdOhX RiNdW RbJfU RbIkIfU RdCaN R[XRY RZJ\\W',
	    'PoU[Y[ RYMY[ ReUl[ RWHZK RSE^EYJ RUTRW RbMgX RXMTU RRM^M[R RiMfUbY][ RbIeU RbIkIgT RcD`N',
	    'PoaIlI RRI^I RSX\\X RSPSZ RXCXO RSP\\P\\X RbC_M RaIeTiYmZ RjIgTcX^Z',
	    'PoeGlG RUM\\M RTZ]Z RTQT[ RTQ]Q][ ReCeM RgYl[ RcTjZ RYD_J RWGRL RaNeW RYCVI R`MjMgTbY_Z',
	    'PoRG_G R\\R^R RSKSR RXCX[ RSK^K^Q RfUl[ RZS]W RXR\\U RbY^[ RfT`Z RWTRY RcNhX RiMeW RXPVV RbIeU RbIkIfU RcC`N',
	    'PoRG`G RYCYG RfUl[ RUP]X R[J^M RYUR[ RcY^[ RfT`Z RcNhX RUJSN R[NWW RbIfU RbIkIfU RcC`N',
	    'Po`IlI RRM\\M RXCX[ RWN\\T RSETJ RaMdUhYlZ RXMSV R\\EZJ RhMeU`Y\\[ R`IcS RaC^N RiIfT',
	    'PoXH]H ReHkH RRM^M RT[X[ RXCXZ ReCeM RdTj[ RbY^[ RhS`Z R[P^V RbNeV R`NjNfU RTPRW',
	    'PoTZ]Z RTRT[ RTR]R][ RfUl[ RVJ]R RfUaZ RcNhX RhPdX RWCSK RVG]F[KVQRR RbJeT RcD`N RbIjIgU',
	    'PobIlI RRK^K RSY]Y RSRS[ RYDYR RSR]R]Z RfUl[ RfUaZ RcMgW RhOdX RbJeT RiKfV RcC`N RiIgT R]DSE',
	    'PodHlH RUK]K RRP^P RU[Y[ RYKY[ RdCdM RYC^I RWERJ R\\R^W RaNcUhYl[ RUSRX R_NkNhTdX][',
	    'PoRM_M RYU`U RVU^U RSV[V RV[Y[ RYSY[ ReUl[ RbX][ RfT`Z RUP^PYT RcNgX RTD^I RiLeV RXITR R\\ERJ RbIeU RbJkIgS RcC`N',
	    'PoUG_G RRQ`Q RTW`W RX[\\[ RYKYW RVCRK RUKURSX RUK^K^U\\[ RbInI RbIgUkYo[ RkIhTdX_[ RbInI RdE_O RbIgUkYo[ RkIhTdX_[',
	    'PoTE`E RcIlI RVI_I RTN`N RXNXX RgVl[ R_Q]T RfU`[ RZQ]T_Y RdPiY RiOeX RcJgV RjJfU RdDaO R\\WUY RTETQRZ',
	    'PoRH_H RU[Y[ RYCY[ RgVl[ R[R_V R\\D_F RVTRV RTKUP RdNhX R^K\\O RiMfUbY][ RcIfU RbIkIfU RdCaN',
	    'PoYNaN RYSaS RVHV[ RYHYU R]C][ RYHaHaU RhWl[ RgUb[ RfQiY RiPeX RWERO ReCdLgV ReIkIhV',
	    'PoTKTR\\RZZ RTK]K]R RfVl[ RfU_[ RVGRJ R[D]J RcOhX RhNdW RWDUI RTZR[ R_XZZ RbIfU RbIkIfU RWRUY RcC`O',
	    'PoSD^D RRO^O RTZ]Z RTST[ RSI\\I\\O RTS]S][ ReUl[ RfU_[ RbNgW RhNdW RbJeU RbIjIfU RcD`P RWEUO',
	    'PoRG^G RSQ]Q RSKSQ RXCX[ RSK]K]Q RfUl[ RZS]V RXQ\\U RfT`[ RWTRY RcNhX RiMdW RXPUV RbIeU RbIkIfU RcC`N',
	    'PoSG]G RRK_K RRP^P RX[\\[ RXCXK RfUl[ RcY^[ RfT`Z RcNhX RbIfU RbIkIfU RcC`N RXMWPUY RVT]T\\[',
	    'PoSJ\\J RSO\\O RST\\T RSDST RSD\\D\\T R[V\\Y RUVR[ RaImI RcD^N RaIeTiYmZ RjIgTcX^Z',
	    'PoUK]K RSO_O RU[Y[ RYKY[ RfUl[ RYC_I RbY^[ RfT`Z RWFRK R\\R^W RcNgX RiMeV RXCVH RURRX RbIeU RbIkIgT RcC`N',
	    'PoTG]G RRK`K RU[Y[ RXCXK RYRY[ RUO^NZR R^TYUTU RbInI RdD_N RbIgTkYoZ RkIhTdX_Z R_D[KVPRR',
	    'PoTQ]Q RTJTQ RZQZZ RTJ]J]Q RfVl[ RTDVH RfU_[ RcNhX RhNdW R\\DZI RTZRZ R_XZZ RbIfU RbIkIfU RcC`O RWQUY',
	    'PoUK]K RYOYT RgVl[ RYE^J RfT`[ RYERK RUPUU RdOhX RhNdW R^PZX RbIfV RdCaM RbIjIfU R^WSY',
	    'PoWZ\\Z RWSWZ R\\W\\Z RfVl[ RXQZS RXD\\F_I RfT`Z RYCSJ RSM^MZP R^S_X RXHYK RdOhX RbIfU RbIkIfU RTTSY RcCaN',
	    'Po\\[^[ RTKT[ RYCY[ RTK^K_[ RgVl[ R]E_I RfU`[ RdOhY RiNeW RUESJ R[O\\U RcJfV RcIkIfU RdCaO RVOUV',
	    'PoWU[U R[[^[ RTKT[ RWOWW RYCYK RWO[O[U RTK^K_[ RgVl[ RfU`[ R]E_J RdOhX RiNeW RUESJ RcIfV RcIkIfU RdCaN',
	    'PoTN_N RRU`U RTHTT RWCWT R[C[T RTH_H_T RgVl[ R[V^Y RWVR[ RfU`[ RdOhY RiNeW RcJfV RcIkIfU RdCaO',
	    'PofHlH RRPXP RfCfM ReUk[ RTHYL R[G`L R\\S_WZ[ RWJRN R_IZN RTSXWR[ RcOgW RREXEUM RXRUY RZEaE]L RZPaQ]X RbNkNgUcY`[',
	    'PoeHlH RTI]I RTM]M RTDTM RTD^D^M ReCeM ReUk[ RhTa[ RbNfW R`MkMfW RVMSS RXPUW R[RWZ R]ZZ[ RUQ^Q]Z',
	    'PoSE^E RTN\\N RTR\\R RTITW RXEXI RRI^I\\[ R\\VRX RbInI RcD_N RaIfTjYnZ RkIgTcX_Z',
	    'PoRG_G RRL_L RTS]S RTV]V RZ[][ RTOT[ RVCVL R\\C\\L RTO]O][ RbInI RdD_N RbIgTkYoZ RkIhTdX_Z',
	    'PoeHlH RSI^I RSN^N RRQ_Q RSESN RYEY[ RSE^E^N ReCeM R\\U^W RWTRY RaNdUhYl[ RYT\\U RYPVU R_NkNhTdX^[',
	    'PoeHlH ReCeM RdTjZ RTFUI RXT]Y RWWS[ R^R[T RdY_[ RhSaZ RXJTN R]E\\I RTQUU RaMeV RXJ]L R`MjMfV RYCWK RYOWX',
	    'PoRF_F RTM]M RSQ^Q RU[Y[ RTITM RYCYG RYTY[ RTI]I]M ReUl[ RcNgW RcF_N R]QYS RiLfTaY][ RbIeU RbIkIgS RcC`L R_UYURV R[UUV',
	    'PoRF_F ReHlH RTWYW RY[][ RTQTX RTQYQYW RRM^M][ ReCeM RXH\\J RbOeViYl[ RaNkNhTcY_[ RWIRK RYCXI',
	    'PoSG^G RRN^N RTZ]Z RTRT[ RTR]R][ RfUl[ RfUa[ RUIWM RXCYG RcNhX RhPeX R\\HYN RcJfV RdCaN RcIjIgV',
	    'PoU[X[ RRJRM RYQYZ RRJ_J_M RfUl[ RSDTH RbY^[ RfT`Z RTN]NYP RWDXG RdNhX RiMdV R]D\\H RbIfU RcIkIfU RdCaM R^SRT',
	    'PogVl[ RTGYL R\\SaX RSSXX R\\G`L R^UZZ RgUa[ RWISM R_IZN RVVRZ RePiY RiOdX RSEYEUL RRPXQUX R[EaE^K R[P`P]W RcJgV ReCbN RcIjIgU',
	    'PoTJ^J RTN^N RRQ_Q RTFTM RXCXF RTF^F^M RfUl[ RfT`Z RcOhX RiNeW RTZR[ R\\[Y[ RbIfU RbIkIfU RYNXQUY RcC`N RVU]U\\[',
	    'PoSF`F RUVZV RX[\\[ RUOUW RWCWH RUOZOZV R\\C\\I RUJSP RUL_L^S][ RbInI RdE_O RbIgUkYo[ RkIhTdX_[',
	    'PoTH^H RTL^L RRO_O RTDTL RTD^D^L RgVl[ RfT`[ RVURY RdOhX RiNeW RVOST RWRUX R[RXZ RcIfV RcIkIfU RdCaN R^[Z[ RTS_S^[',
	    'PoeHlH RRI_I RSY^Y RTMTZ RREYEZX R[N^N^Y ReCeM RbOeViYl[ RaNkNhTcY_[ RWMSM RSSWSSS R^SZS R^DUE',
	    'PoeHlH RSH^H RSK_K RSV^V RSZ^Z RSDSP RSRS[ RSR^R^[ RSD^D^H ReCeM ReUj[ RZN]P RbOfW R`NkNhUcY`[ RYH[O RXORP',
	    'PoRI_I RRR_R RYCYO RTDUG RYISN R]D\\G RYJ^M RTV]Z RWOTW RcF_O R\\R[WXYS[ RbInI RdD_N RbIgTkYoZ RkIhTdX_Z',
	    'PoeHlH RSH^H RRL_L RTS^S RSESH RTOTS RYCYH RTO^O^S R_E_H ReCeM ReTk[ RhTa[ RUUVY RbNfW RaMkMfV R\\T[X R_XRY',
	    'PoRG_G ReHlH RUN\\N RVX[X R[[^[ RSPS[ RUIUN RVSVY RYCYG RVS[S[X RUI\\I\\N RSP^P^[ ReCeM RdTjZ RhTa[ RbNeW R`MjMfW',
	    'PoSG^G RRK_K RSS^S RSDSH RSOSS RYCYG RSO^O^S R^D^H RgVl[ RfU`[ RUUVY RdOhX RiNdW RcJfU RcIkIfU RdDaN R\\U[YRZ R_XUY',
	    'PoRF_F RSL^L RTSjS R_WhW RR[l[ RSHSL RWVW[ RYCYQ RSH^H^L R_S_[ RbGdLhPlQ RWL^P RcCaJ RYLUPRQ RbGkGhLeO_Q',
	    'PoRF`F RUO]O RVW\\W R[[_[ RSKS[ RVSVY RUGUK RYCYF RYKYS RVS\\S\\W RSK_K_[ R\\G\\K RcIoI ReE`O RcIgUkYo[ RlIiTeX`[',
	    'PoYGcG RZNbN RZRbR RRDR[ RZJZR R^C^[ RZJbJbR R]RbV RVLXQ R\\TXY RgVc[ RgRk[ RjPeY RREXEUK R^P[V RfMiX RWTTT ReIhW ReIkIhU RfCdO',
	    'PoRF_F RSL^L RSO^O RRR_R RX[\\[ RSISO RYCYOYR RSI^I^O R[D^D RTZR[ RWRUY RVU]U\\[ RbInI RdD_N RbIgTkYoZ RkIhTdX_Z',
	    'PoRHaH RTK_K RSQ_Q RRTaT RSNTQ RTETK RTE_E_K RSN_N_Q RgVl[ RfU`[ RdOiY RUV^Z RiNeX RYCXQUX RcIgV RcIkIfU RdCaO R]UZYS[',
	    'PoXF^F RTL\\L RRR^R RRIRM RXCXI RYMY[ RRI_I_L RfUl[ R[T^W RTNUQ RgUa[ RUTSX R]N[P RdOiY RiNeX RcIgV RcIkIgV RdCaO',
	    'PoRE^E ReHlH RVM]M RZU^U RRX_X RRERZ RTOTU RVHVM RZOZU RVH]H]M RZO^O^U ReCeM RdTjZ RbOeW R`NkMhTcY^[ RTOXOXUTU',
	    'PoVOhO RVQhQ RVThT RXVkV RVLVS RVLhLhS RTHiHiK R]TTX RZERH RXFcE_IYJ RVGUK R_IfJ RgVcX_YU[ RYW`Yk[',
	    'PoRFaF RfHlH RSI`I RTO^O RRSaS RTZ^Z RTLTP RTVT[ RYCYI RTV^V^[ RTL^L^P RfCfM ReTk[ R\\Q[S RhTa[ RVQWS RbMfV RaMkMfW',
	    'PoRH`H RfHlH RVN]N RWW\\W R\\[_[ RSQS[ RVJVN RWTWY RWT\\T\\W RTK]K]O RSQ_Q_[ RfCfM ReUk[ R[H_L RhTa[ RXHSM RcNfW RaMkMfW RSEYEVK R]CUD',
	    'PoSG_G RTJ^J RRM_M RRR`R RTZ^Z RTUT[ RYGYR RTU^U_[ RgVl[ RUNVQ RUCWG RfVa[ R\\C[G R]O[R RdOiY RhQdY RcJgV ReCbN RcIjIgU',
	    'PoSHaH RcIlI RVN]N RWX\\X R\\[_[ RSQS[ RVJVN RWTWY RWT\\T\\X RTK]K]O RSQ_Q_[ RgVl[ R[H_L ReWa[ RXGRM RdOiY RhPdY RSEYEVJ RcJfV RiKfW RdCbN R^CVD',
	    'PofHlH RSI`I RTU\\U RVY\\Y RSYYY RSESI RTPTT RXNXX R[E[I RTP]P]T RSE`E`I RfCfM RfUk[ R[V]Y RVKRO RcOgX RbNkNhUdY`[ R_[[[ RWEWIUM`M`W_[',
	    'PoSI_I RSL_L RRP`P RRT`T RSW_W RSDSI RWDWI RYJYO RYTY[ R[D[I RSD_D_I RgVl[ RfU`[ RVQVT RdOhY RiNeW R\\Q\\T RcIfV RcIkIfU RdCaN',
	    'PoVI]I RTRXR R[R_R RTMTR RTMXMXR R[M[R R[M_M_R RgVl[ R\\V_Y RVVXY RYC_I RfU`[ RXERK RdOhY RiNeW RVTS[ R]TZZ RcJfV RcIkIfU RdCaO',
	    'PobGkG RSQkQ RTGTN RVIVM RYCYN R[I[L RbRbZkYk[ R\\D_F RUDRG RcE_I RbGfLlN RZQUU[X RiHeL`N RXSRV RWT^TZXT[ RTG^G^N[N RjTaV',
	    'PoTE^E RbFlF RSH_H RUNlN RWQkQ RYCYL R`O`[ R[S\\U RfSiV RZSWV RfRcV RVKRL RYHUL RcE`H RWH^K R`TcWkZ RbFeIlL RjFfJ`L R`T]WVZ RUNUSTY',
	    'PofHlH RYU`U RU[Y[ RRNRQ RSVZUY[ R\\E_E_N RTESN`N`Q RfCfM ReUj[ RZEWH RXJZJWL RbOfW RUQ^QYS RbNkNhTdY_[ RWDSD R_G\\G RTJVJTJ R\\J_J\\J RVGSH R\\UWU',
	    'PocIlI R\\J_J RXV_V RVV]V RRVZV RU[Y[ RRNRQ RZTZ[ R\\D_D_N RTESN`N`Q RgVl[ RXIZL RXEZG RfU`[ RZDWG RZIWL RdOiY RiNeW RTQ^QYS RWDSE RcIgV RjIfU RdCbM R_G\\G RSJVJSJ RVGSG',
	    'PoRHlH R_C_H RWHZPaVj[ RgHcP^VR[',
	    'PoSFkF RRTkT RZ[_[ R_C_F R_S_[ RWPfP_S RfFdH^KTN RYG_KdLlN',
	    'PoRFlF RWVgV R_C_F RYF[I`KkN RfFcI]LRN RWNWTVYS[ RWQgQ RgNg[',
	    'PoRFlF RROlO RWRgR RWUgU R\\XkX RXYgY RRYbY RWOWY R_C_F RgOg[ RYG_JcKlM RfG`J[KRM',
	    'PoSFkF RSPkP Rf[i[ RUTU[ R\\T\\Z RbTbZ RUTiTi[ R^C`F R_P]S RfFdH_K[LRN RYH_KcLlM',
	    'Po]FdF RRI\\IlJ RaQeQ RWCWI R]O]Y RaLaY RhDkH RSMWPZV RXPTY RYIWV RgCfPhZ ReXZY RlWk[i[',
	    'PoSFkF RWQgQ RWTgT RWWgW Rb[k[ RWOWV RbWb[ RWOgOgV RkXk[ R^C_F RZH`KiN RfFdH^KTN R_KlN R\\WYZR[',
	    'PoRFkF RYOfO RXRfR R[[_[ R_C_F R_R_[ RhNh[ RdUeY RZUXY RXF[H_KcLlM RfFdH_JZLRM RVNVSUY',
	    'Po^KjK R^PjP R^F^[ R^FjFjP ReUjZ RVVRZ RWPZW RUMWQ RVCWG RcPfW RcCeF RYNUX RkSeU RRI[IWU RcY]Z',
	    'PoSF\\F RbFkF RTI\\I RbIkI RbMlM RRRlR R_O_R RbCbO R\\C\\LXOVP RXR\\VaXeZl[ RfRcV^XYZR[ R[LRM',
	    'PobElE RRFZF RSNZN RcPkP RaZlZ RVFVW R_C_I RgEgZ R[M_QaU RZVRW R[HcH^TZ[',
	    'Po^MlM R_UlU Rj[l[ RXCXI R^E_R_[ RdPd[ RhPh[ R^HlHlM R_PlPl[ RVURZ RTLXQ[V RYNUX RRI[IWU R^M]X ReD^E RjD`E',
	    'Po_JiJ R_SiS Rh[l[ R\\I\\[ R_M_S RdHdY R_MiMiS RaElEl[ RUDWF RdRiW R\\D_G RdR_X RSMVPYV RbNbQ RgOfR RWPTX RRIYIVU',
	    'PoRG^G R`QkQ RSU]U RSQSU RXNXZ RSQ]Q]U RaGaQ RhFkFkQ RhWl[ RWDXF RcW`[ RcF`G ReCeRiY RTI[L RfQbY R[HWMRN R`KcK`K RhKkKhK ReNgU ReMdV R\\W]ZRZ',
	    'Po\\HbH ReHlH R\\KbK ReKlK R^NiN R_UiU Rh[l[ RVCVI R\\D\\[ R_Q_U R\\DbDbK RdLdZ ReDeK R_QiQiU ReDlDl[ RdTiY RdT_Y RTLYW RWQSZ RXLUW RRIYIVV',
	    'PoRFlF RUNYN ReQiQ RUQYQ RR[l[ RSSSV RVLVS R[L[S R[LcLcS RWUgUg[ ReNiMiS RSSkSkV R^C`E RYLUL RfLiL RcYgY R]MaN]O]Q`P`Q]Q RWUXZ[YWY RXF_IkJ R[Y^YbY RhFdG_IRK RWWgXcY`Y]Y',
	    'PodCd[ RXE_H RTL\\N RlRRU',
	    'PohCh[ RXO[R RTNYP RbMeP RbEeI RZUT[ RXESP R]MYW RXCUL RWH^H[T RlS_U',
	    'PoRM]M RWCW[ RhCh[ RWNZQ[T RSETJ R_McO R`FdG RWMRW R\\EZJ RlS^U',
	    'PodIkI RaVkV R]WgW RZWdW RUW_W RRW\\W RSLSO RdNd[ RSLlLlO R[F^I RhFlI RYDSI RfDaI RZN`Q RWR]T R\\HUI RgHaI',
	    'PoUO_O RUT_T R[[_[ R[I[T RUJ_J_[ RiCi[ RbLeP RbEfI RVF\\FZK RlS`T RWCTJUTTZ',
	    'PoUK^K RSP_P RV[Y[ RYKY[ RiCi[ RbMeO RYC^H RbEfI RYCRK R]S_X RUSSX RlR`T',
	    'PoTI]I RaIjI RaVjV R^WgW RZWdW RVW_W RSW\\W RSLSP RTDTI RTD]D]I RaDaI RdNd[ RaDjDjI RSLkLkP RZN`Q RWR]T',
	    'PoSLWL R\\L`L RSQ`Q RTFTQ R\\F`F`Q RiCi[ RZCYS`Z RbMfP RcFgI RXVS[ RWESF RlS`U RYMXV',
	    'PoRF`F RUJ]J RUM]M RRQaQ R[V_V RTY`Y RTQT[ RUCUQ R]C]Q RiCi[ RbLfP RcEfI R[Q[V RXQWV RlSaT',
	    'PoRF`F RSI_I RRL`L RRS`S RRV`V R[[^[ RTOT[ RVCVL RYLYV R\\C\\L RTO^O^[ RhCh[ RbLfP RcFfI RlSaT',
	    'PoRG]G RSN\\N RSR\\R RRV^V RSJSR RXCXJ RXRX[ RSJ\\J\\R RgKg[ ReDlK RdE^K RaLeN R_PcR RkT_V',
	    'PoSF^F RRJ_J RSR^R RSV^V RSNSV RVCVJ RYJYV R[C[J RSN^N^V RhCh[ R[W^Z RaMeP RbFeI RVXRZ RlR`T',
	    'PoTI`I RTU]U R\\[_[ RTETI RTPTT R\\E\\I RTP]P]T RTE`EaI RiCi[ RcLfP RcFgI RXEXIVKRO RlSaT RUM`M`W_[ RYNYXSY R\\V]YUY',
	    'PoVMlM RbMb[ RhDVFVSSZ',
	    'PoVF\\F RVLlL RbLb[ RZQjV RVFVPTX RhD\\F',
	    'PoTL_L RaLlL R[L[[ RhLh[ R]DTFTSS[ RjDaFaQ_Y',
	    'PoWSkS RaSa[ RcDkG RZDSH R[HaKkN RbH\\KRN RWOVTVX R^OVP RdN[O',
	    'PoSKZK R`LlL RRQZQ RSDSK R[C[[ RgLg[ RUQT[ RkD`F`Q^Y',
	    'PoRG^G RaMlM RSO^O RYKY[ RhMh[ RWCTO RkDaFaR`Y R^TRV',
	    'PobLlL RUTZT RX[\\[ RULUV RULZLZT RhLh[ RVCRL RUH^G^T][ RkDbFbR`Z',
	    'PoRE\\E R`MlM RUW\\W RUJUY RUM\\M\\W RgMg[ R`F`T\\[ RWESR RkD`F R`O^X',
	    'PoRG^G RaMlM RSN^N RSR^R RRV_V RSJSR RXCX[ RSJ^J]R RhMh[ RkDaFaQ_X',
	    'PoTL^L RaLlL RRX]X RRDRY RYCYV RhLh[ RYM]Q RUEVI R]E[I RkD`F RaEaR_Z RYLTS',
	    'PoRG_G RRL`L RbMlM RTU^U RTZ^Z RTPT[ RVCVL R\\C\\L RTP^P^[ RiMi[ RkDbFbRaY',
	    'PoRG_G RUL]L RbMlM RUP]P RRT`T RUDUT R]D]T RiMi[ R[V^Y RVWRZ RlDbFbR`Y',
	    'PoSF_F RRM_M RaMlM RRQ_Q RYCYF RYMY[ RhMh[ RYR\\V RYQWTRX RUHUL R\\H[K RkDaFaQ`X',
	    'PoSG\\G RRL_L RbMlM RUU]U RUY]Y RVPV[ RXCXL RTQ]Q]Y RhMh[ RZN\\O R\\ITQ R^E\\J RkDbFbRaY',
	    'Po[J_J RbLlL RSNWN R[T_T RWFWN RSNSTWTWY R[E_E_J R[E[N_N_T RhLh[ RXCSESJWJ RkDbEbRaX R[T[XRZ R]XTY R_XWY',
	    'PoRF`F RVI\\I RbMlM RSP^P RSS_S RTV^V RRY_Y RVCVI RYIYY R\\C\\I RSL^L_P RiMi[ RSLTP RkDbFbSaZ',
	    'PoSHaH RcLlL R[QaQ RYWaW RZCZN R[Q[W R_N_[ RiLi[ RUDVG RTSWWS[ R_D]G RYIaL RUNRU RURYRVY RZHVLRN RkDcFcSbZ',
	    'PocLlL RRN`N R[V_V RRZ`Z RSESK R[R_R_V RhLh[ RXDSE RWVWZ R[V[Z R[N[R RSRSVWV R\\E[K RWGYJRK R_K]K RWNWRSR RZE`E_L RkDcFcUa[',
	    'Po^IlI RTL\\L R_UhU RYLY[ R^D^I R_P_T RcNcX RgEgI R^DlDlI R^YcYhWhY RbEcHaK\\O R_PhPhU Rk[g[ R_MlMlVk[ R[DTFTTR[',
	    'PoSKYK RZK`K RbMlM RRM`M RZW`W RRX_X RRDRZ RiMi[ RTGVI RTQVT RVDUG RSWXVXW RWFUK RVOVQUV R\\D[G]H\\K R\\N[Q]S\\V RlDbFbSaY',
	    'PoTGaG RVIYI RdLlL RVPaP RVW^W RUZ^Z RVMVP RYMYP RZRZY R[I[L R]M]P RVUVV^U^W RVMaMaP RWSaSaZ RTDaDaG RiLi[ RXQUT R`[^[ RYKVK R]I`J]K RTDTSR[ RkDdEdSb[',
	    'PoRHlH R[OhO R`[f[ R_C_H RhOf[ R\\H[PYVTZ',
	    'PoRH]H RV[Z[ RUM[M[Y RhMh[ ReElL RdCbG]L RWCXG RUHUPSZ RaMaS`X',
	    'Po`HkH RRH]H Rh[l[ RUM\\M\\Y RaLhLhZ RVCWG RlWl[ RbC^L R[ZW[ RUHUQTZ RaLaS`X',
	    'PoRH]H RV[Z[ RXCXH RbNeOgR RgHlM RbI]M ReChJ R`VhY RdCaK RVHVPTY RVM\\M\\V[[',
	    'Po`HlH RRH^H Ra[k[ RW[[[ RXCXH RVN[N[[ RaLa[ ReJeW RlXl[ RaC]L RjL_Q RkLjUgU RVHVPTY',
	    'Po`HlH RRH]H RV[Z[ RUM[M[Y R_M_X ReGe[ RaC]L R_MkMkWhW RXCWHVPTZ',
	    'Po`HlH RRH^H R^SlS RV[Z[ Ra[e[ RXCXH RePe[ R`LjLfP RbC^L RVHVOSZ RVN\\N\\UZ[',
	    'PoRH\\H R`KiK R^KgK Ra[d[ RdMdZ RVCYF RjIlM RhGjJ RaG^K RiOkW RcCaH R`O]W RZZW[ RUHUQTZ RUM[M[VZ[',
	    'PoSFkF RRPlP R`[f[ RSKSN R_C_F R_M_P RSKkKkN RYGYJ ReGdJ R\\QZWWYR[ R[UgUf[',
	    'PoaHlH RRH^H RaQlQ RW[[[ RXCXH RgQg[ RbC^L RVHVOTY RVN\\N\\V[[ RjKaLaS`Y',
	    'Po_HlH RRH]H R]TlT Rf[j[ RV[Z[ RUM[M[Y R_L_[ RdLdS R_LjLj[ RaC]L RXCWHVPTZ',
	    'Po`HlH RRH]H Rc[l[ RV[Z[ RdLd[ RlXl[ RaC]L RiJ^N RXCWHVPTY RVM[M[WZ[ RjO_Q RhT^V RlTaU',
	    'Po`HkH RRH]H RW[[[ RXCXH RbMb[ RaC^K RhK^N RVHVPTX RVM\\M\\V[[ RdLgWl[ RkOfQ',
	    'Po_HlH RRH]H R]LlL RhYkY RUM[M[Y R_Q_Y ReIe[ R_QkQkX RaC]K RZ[V[ RXCWHVPTZ',
	    'Po`HlH RRH\\H ReIeP RWCXG RbPdUgYl[ R`KjKjO R`QjQhUdY_[ RZZWZ RUHUQTZ RUM[M[VZ[ RbC_K`Q^Y',
	    'Po]GkG RRH\\H R]MjM Ri[l[ RdPdZ RiPi[ RgJlN RbG_M RlXl[ RdCdG RY[V[ RWCWHUPSZ RUM[M[VZ[ R_P_T^Y',
	    'PoSElE RUKiK RUNiN RRRlR R`[f[ RUHUP R_C_O RUHiHiP R[VhVg[ R\\RZWXYR[ RcDgD',
	    'Po`HlH RRH]H R_KlK RW[Z[ RXCXH RlXl[ RdRfV RaC^K RbOiOiVj[ RhX]Z RVHVQTY RVM\\M[VZ[ RbKaR`Z',
	    'Po`HlH RRH]H ReRkR Rd[l[ RV[Z[ ReLeZ R_LkLiO R_Vd[ RaC^K RXCWHVQTY RVM[M[WZ[ R`R_Y',
	    'Po_HlH RRH]H R`NlN R`SkS R][l[ RW[Z[ ReIeZ RaC]K RaJ^Q RVM\\M[W[[ RXCWHVPUX',
	    'Po`HkH RRH\\H RUM[M[Y RdNfQ ReIiNlO R`N]O ReI_O R_SjRdX RWCXF R`VhZ RaD^J RZZWZ RVHUQTZ',
	    'Po_GlG RRH\\H R_OkO Rb[l[ RV[Z[ RcQc[ R_JkJkO RVM[M[YZ[ RlXk[ RjSbU RXCWHVPTZ R`C^J_S]Z',
	    'Po`HlH RRH]H R`MkM R^SkS RW[Z[ RXCXH RaC^K RbI_P RVHVOTY RVN\\N\\U[[ ReMeRcV_[ ReRgWk[',
	    'Po_HlH RRH]H Rf[l[ RV[Z[ RUM[M[Y RgIg[ RlXl[ RfQkU RaW][ RjLiO R^L`P RaC^J RbS^T RXCWHVPTZ RbIbSaX',
	    'Po^FkF RRH\\H R_VfV Re[j[ RUM[M[Y R_Q_X R_QfQfV R]NkNj[ RVCXF RdHiK RcJ^L ReCcJ RZZWZ RUHUQTZ',
	    'Po^GlG RRH\\H R^KkK R]PkP Rh[l[ RU[Y[ RUMZMZZ RdSdZ RdHdK RiSi[ RlXl[ RgMkQ R`C]J RbK`P R]Z[[ RWCVHUPSZ R_S^Y',
	    'Po`GlG RRH\\H R_QkQ R_UkU R_N_[ ReHeN R_NkNk[ RVCYF R_JaL RjIiM RaC^J RZZW[ Rh[k[h[ RVHVQTZ RUM[M[VZ[',
	    'PoRH[H R`JlJ RbMjM RaQkQ R`UlU Rb[l[ RfCfX RbFjFjM R_S[[ RX[U[ R[F^F\\N`N^X R\\R^WaZh[ RWCVHUPSZ RUMZMZVX[',
	    'PoRF^F RXQ[Q RTY\\Y Ra[k[ RTST[ RTS\\S\\Y RaRa[ RXCYF ReDjI RkXk[ RdD_J RbJhK RaNiQ RjTaV RVFTP RVK\\K[Q',
	    'Po_GlG RRH\\H R_JkJ R^WeW Re[i[ RUMZMZY R^S^Y R^SeSeW R]PkPj[ RdKjN R`C]J ReJbM^O RY[V[ RWCVHUPTZ',
	    'Po_GlG RRH\\H R^KlK RaOiO RaRiR R]VlV RaIaV RiIiV RhXl[ RaY][ R`C^J RVM\\M[X[[W[ RXCWHVQUX',
	    'Po\\GdG RRH[H R[MkM R]TcT R]XcX Ri[k[ R]P]Y R]H^K R`C`F RbHbL R]PcPcX RgUb[ RlWk[ RiDkH RjOfW RY[V[ RgCgOgRhZ RUNZMZWY[ RWCVHUQSZ',
	    'PoRH\\H R]JlJ R^VjV R^ZjZ R^Q^[ R]EdEdP RdQdZ R^QjQj[ RdIiOlP RdJ]P RhFgI R_F`I RZ[W[ RWCWHVPTX RVM[M[VZ[ RiCaD',
	    'Po_FlF RRH[H R^IlI R^PkP R^SjS R\\VkV Ra[d[ R^L^P RbIbP RdVd[ RgIgP R^LkLkP RhWkZ RVCWG R`W][ R`D\\H RY[V[ RUMZMZWY[ RVHUQTZ',
	    'Po_GlG RRH\\H R]NlN R^WjW R^ZjZ RU[Y[ RUNZNZY R^T^[ RdJdR RdTdZ R^TjTj[ RhKgM RaL`O RdNgQlS R`C\\K RdNaQ\\S RWCVHUPSZ RiI_J',
	    'Po^FlF RRH[H R]IlI RaPgP R^RjR R_VjV R_YjY R][l[ RV[Y[ RUMZMZY R^K^R R_T_Y RaMaOgMgP R_TjTjY R^KjKjR RVCWF RcGeI R_C]H RUHUQSZ',
	    'Po^GkG RRH[H RaKgK R]SkS R^WiW R^ZiZ RU[Y[ RUMZMZY R]N]R R^T^[ RdNdR R^TiTi[ R]NkNkR RVCWG RdHkL RgOgQ R_D]I RdH\\L R`O`Q RUHUPSZ',
	    'Po^GkG RRHZH R`KkK Rb[l[ RfQkU R[O^O^X[Z R_D]H RUCVF R]K^M R^Xd[ RiIdM`O RjNfQ`R RfSaV RX[U[ RfXdX RUHURSY RUMYMYWX[ RfOgTfX',
	    'Po^HlH RRH[H R^NaN RhRjR R\\VlV R^K^V RcPcT RcHcMfNfV RhJkJkV RaJ^K RfXkZ R_C\\J R`Y\\[ RcKfKbK RjNgN R^RaR^R RY[U[ RWCVHUPSZ RUNZNZVY[',
	    'PoTEjE RRNlN R`[k[ RaPaZ R\\TXXR[ RlVk[ R_E^L]T',
	    'PoTEjE RSOlO R`[k[ RaPa[ R^P\\UYXR[ RlVk[ RUHRQ R_E_L]T',
	    'Po^EkE RTJ\\J R^NlN RTO\\O Rf[l[ RTETX RTE\\E\\O RaEaN RgMgZ RlWl[ RgMcU_Y[[ RhHdT RYR\\T]WRX RhEeR',
	    'Po^EkE RRJ\\J RRN\\N R^OlO RRY\\Y Rf[l[ RRFRN RSPSX RRF\\F\\N R\\V\\Y R`G`N RfNfZ RlWl[ RWCWF RfMcU_YZ[ R\\QRS RhEfS',
	    'Po`ElE RaOlO RUW[W Rg[l[ RZ[^[ RRNR[ RUQUW RTIYIYN RUQ[Q[W RTD\\D\\N RTDSN^N^Z RhQhZ RlVl[ RfPaY RbHaN RfEfMcW RfHeT',
	    'PoROlO RRYlY RRER[ RRElEl[',
	    'PoWMgM RWUgU RR[l[ RWEWU RWEgEhU',
	    'PoZOlO RZYlY RRDR[ RZEZ[ RZElEl[',
	    'PoSLkL RSUjU RSZjZ RSCSK RSPS[ RSPjPj[ RlHkK RiESG',
	    'PoUIhI RUNhN RRTlT RUEUN R_N_[ RUEhEiN',
	    'Po[MlM RRMYM RRVYV RRERX RREYEYV RcDc[',
	    'PoRMYM RRVYV Rd[j[ RRERX RREYEZV R`UZ[ RbP^X R\\FlFlSj[ RcFcLaU',
	    'PoXQbQ RXWbW Rc[j[ RXLXY RXLbLbW RXHlHlSj[ RYCVJRN',
	    'Po`MjM R`UjU R\\ZkZ RSI\\I\\Z R`E`U R`EjEjU RlWkZ RVTR[ RWCWPUX',
	    'PofNlN RVUhU RVZhZ RVPV[ RSGfGfN RVPhPh[ RlKlN R\\IYMSO R]C[K',
	    'PoUIhI RUMhM Rf[l[ RUEUM RTQfQfZ RUEhEhM R\\UXXR[ RlWl[ R\\O[V',
	    'Po]EkE RRMYM R\\NlN RRVYV RRERX RREYEYV RdEd[',
	    'PoVIhI RVMhM RTPjP RRUlU RVEVM R_P_[ RVEhEhM',
	    'PoVJiJ RVOiO RRSlS RVEVO RVEiEiO R`TcXgZl[ R_O]UZYR[',
	    'PoRMZM RRVZV Rc[j[ RRERX RREZEZV R`NeS R`C\\L R_HlHlRj[',
	    'Po]EkE RRMZM R[NlN RRUZU R_[d[ RRERX RREZEZU RdEd[',
	    'PoTIjI RTNjN RRYlY RRRR[ RTETN R_P_Y RTEjEjN RlRl[',
	    'Po]IlI RRNZN RRVZV Ra[g[ RRFRX RRFZFZV RhCh[ RaPcS R_MaP',
	    'Po]HlH RRMYM RRVYV RRERX RREYEYV RdDdF R]H]Q\\X',
	    'PoRMXM RRUXU Rd[h[ RRERX RREXEXU RbN\\T R\\EhEdH RhLeT_Y[Z RcI\\LlLjZ',
	    'PoTIjI RRMlM RVUhU RVZhZ RTDTI RVQV[ R_C_M RVQhQh[ RjDjI',
	    'Po[ElE RRNYN R\\PkP RRWYW RZ[l[ RRFRY RRFYFYW RcEc[',
	    'PoVIiI RVMiM RRQlQ RVDVM R_N_Q RVDiDiM RWQ\\VbYj[ RgQbV]XT[',
	    'PoZIlI RRNYN RRVYV RRFRX RRFYFYV RbCbI R]JaSfXl[ RiIdS_XY[',
	    'PoRMYM R\\SjS RRVYV Rc[k[ RRERX RREYEYV R\\K\\V RcCc[ RjKjU RlWk[ RjF\\H',
	    'Po^FfF RSFaF RTLiL RUWiW RUZiZ RTHTN RUSU[ RUSiSi[ RiHiM R_C_PkOkP RiDbE',
	    'Po^HkH RRNYN R[PlP RRVYV RRFRX RRFYFYV RdHd[ R_D[M',
	    'PoRMZM R^NgN R]TiT RRVZV Rc[j[ RRERX RREZEZV R`C\\M R^HlHlSj[',
	    'Po[HlH RRMYM RRVYV RgXkX RRERX RREYEYV R\\M\\X RcCc[ R\\MkMkW',
	    'PoVHkH RVMkM RgYlY RUPUY RVDVM RaPa[ RVDkDkM RaPlPlY R^OTP R]WSY',
	    'PoVHhH RVKhK RTNlN RVEVK RVEhEhK R`QbVeXj[ R`Q]V[YV[ RTNTUSZ',
	    'Po]EkE RRMYM RRUYU RRERX RREYEYU R_LcTgYl[ R]LjLgTbY][ R]E]Q\\Y',
	    'PoRNYN RRUYU RRFRX RRFYFYU R[VW[ R_McTgYl[ R]PYY R^MjMgTdX][ R^L[X R[EgEfM R_G\\U',
	    'PoUIiI RUMiM RUS^S Ra[k[ RUDUM RUPU[ RaPa[ RUDiDiM RlWk[ RiRaU R^YS[',
	    'PoUHiH RUMiM RRTlT RUDUM ReNe[ RUDiDiM R_NSP RZPYWWZS[',
	    'PoRMYM R^QlQ RRUYU RRFRX RRFYFYU R^JlJlR R^E^R\\X RjD^F',
	    'Po[HlH RRMZM RRUZU Rb[h[ RRERX RREZEZU RcCcH R^VY[ R`OiNiTh[ RaJ_V',
	    'PoVHiH RVLiL RRPlP RSTkT RVDVL RVDiDiL R`UdYl[ R_P]VZYR[',
	    'PoTEjE RRKlK RWUfU RWZfZ RXPX[ RVQgQg[ RbKgQlS R^E[LVQRS',
	    'PoUIiI RUMiM RRUlU RRZlZ RRPR[ RUDUM RUDiDiM RRPlPl[',
	    'PoRO\\O RaOlO RRZ\\Z RaZlZ RRER[ RRE\\E\\[ RaEa[ RaElEl[',
	    'Po`KlK RRL[L R_RlR RRT[T Rf[l[ RRERW RRE[E[T R`DlDl[ R`E`P_WZ[',
	    'PoVIlI RWVhV RWZhZ RVEVO RWQW[ RWQhQh[ RlNkP RVE`FbLeOiP RbD\\E RgD`D R\\NTO R^NWO RaNZO',
	    'PoRNYN RRUYU Rb[g[ RRFRX RRFYFYU R\\NiNiY R_VZ[ R_EfEjL R^G[N RaN^W',
	    'Po^LhL RRMYM RRUYU RRERX RREYEYU RcDkL RbE[L R[QjQe[',
	    'PoRNYN RRVYV Re[i[ RRFRX RRFYFYV R_RZW RcV^[ RfMaX R`D[N RcI]T RgIdU R^IlIlQj[',
	    'PoWIjI RWNjN Re[j[ RWEWN RWEjEjN R_RWZUZ RWRlRk[ R[NVSRU ReRcV_[',
	    'PoTGjG RRMlM RWUgU RWZgZ RWPW[ RZCZM RdCdM RWPgPg[',
	    'Po^MlM RRMZM RRUZU RRERX RREZEZU RfMf[ R^F^SZZ RjD^F R^M]W',
	    'PoRNYN RRVYV RcYjY RaYhY R^ZeZ R\\ZcZ RRFRX RRFYFYV RhTk[ RaEgEkO R_F\\O RbM^Z',
	    'PoVIhI RVMhM RRSlS RVDVM RVDhDhM ReVj[ R_O`S RcVfW RZUT[',
	    'PoVHiH RVMiM RRQlQ R`[f[ RVDVL RVDiDiL R_NaP RZVWYR[ R[QYW R[UhUg[',
	    'PoVHhH RVLhL RUPiP RRTlT R]ZhZ R[ZeZ RYZcZ RUZaZ RVDVL RVDhDhL RdUj[ R[TUZ',
	    'PoRHkH RWUgU RWZgZ RWPW[ RWPgPg[ RUJXM RhJeM RVORQ R[ITQ RbIgOlP R^CZK',
	    'Po^ElE RRMYM R^SjS RRVYV R^ZlZ RRERX RREYEYV R^E^[ R^KjKjS',
	    'PoWHhH RWLhL RSOlO Rc[h[ RWDWL RWDhDhL RYXUZ R^SWY RcU][ RWTRU RZOUU RdSbW RWSjSi[',
	    'Po_OlO RVVhV RVZhZ RVRV[ RVRhRh[ RdGjM RXCWGRK RdI_M ReDbK RWG^F\\KYNRQ RVGXK[NeO',
	    'Po[ElE RRMYM RRVYV Rg[k[ RRERX RREYEYV R\\J\\[ R\\JkJk[ RcEcLgT RcM`T RcHbQ',
	    'PoVIiI RVMiM RURjR RVViV RR[l[ RVEVM R_O_[ RVEiEiM RXORU',
	    'PoRNXN RZQlQ RRVXV RRFRX RRFXFXV R]H]Q R]HjHjQ RcCcQaU^YY[ RcQeUk[',
	    'Po[HlH RRNYN RRVYV RYZkZ RRERX RREYEYV RfTkY RgKfObS R[N^ObS\\Z RcCbH`L]O',
	    'PoRNYN R]NkN RRVYV R\\ZlZ RRFRX RRFYFYV R\\Q\\[ R]F]N RdDdY RkFkN RlQl[',
	    'PoRMYM RRUYU RRERX RREYEYU RcCkK RbD[L RcK`N\\O ReOcR]U RiSeWaY[[',
	    'Po^LhL RRMYM RRUYU ReXiX RRERX RREYEYU RaPa[ R[PiPiX RcCkL RbE[L',
	    'PoTFjF RUJiJ RRMlM RXUfU RXZfZ RYPY[ RYQfQf[ RbJcNgRlT R_D[MVSRT',
	    'PoVHhH RVLhL RRPlP RVDVL RdSd[ RVDhDhL RbPdRkU R^NYSRU RYYT[ RZSYX',
	    'Po\\HjH RRMXM R[OkO RRVXV RRERX RREXEYV RcCc[ RfSlY R`RYY RcMeQgU RcMaP`T',
	    'Po^HlH RbMlM RRMYM RbTlT RRVYV RRERX RREYEYV RbHb[ R`C\\M',
	    'Po[HkH RRMYM R\\OkO RRVYV RRERX RREYEYV RcCc[ RfSlY R`RYY RdOgU RbO_T RcMaR',
	    'PoUHiH RULiL RSQjQ RRUlU RUDUL RePe[ RUDiDiL RcMkQ RYVVYR[ R[MXQYX',
	    'PoRMYM R^UeU RRVYV Rd[j[ RRERX RREYEYV R^M^W R^MeMeU R`C\\M R^IlHlSj[',
	    'PoVHiH RVKlK RYO`O RVP]P RSPZP RVVhV RVZhZ RVDVP RVRV[ RVRhRh[ RkNlQ RVDiEiI R`HaLdNgPkP',
	    'PoRMZM RfNjN RRVZV R]ZlZ RRERX RREZEZV R]Q][ R]QlQl[ R\\ElEkN RcEaJ]N',
	    'PoVHiH RVLiL RSO]O Re[k[ RVDVL RXOXX RVDiDiL RcV`Y\\[ RdNcW R^QlQk[ R^WRX',
	    'PoVHhH RVLhL RRPlP R_UjU RVEVL R`P`Z RVEhEhL RXRS[ RVV[Yd[m[',
	    'PoVIhI RVLhL RSPkP R_UjU RR[l[ RVEVL RWRW[ R_P_[ RVEhEhL',
	    'PoUIhI RUMhM RTQjQ RR[l[ RUDUM RUDhDhM ReSb[ RYS[Z R_M_R',
	    'Po[MkM RRMXM RRVXV RhXkX RRERX RREXEYV RfCf[ R[GkGkM R_V[[ RaCaS]Y R[M[RlRkX R`N_W',
	    'Po]IjI RRNYN R[PlP RRVYV RRFRX RRFYFYV RcCcQfWhZl[ RcPaV]YZ[ R^E[M RcMdU RcLbU',
	    'PoVHkH RVLkL RhXlX RUOUW RVDVL RcOc[ RVDkDkL RcOlOlX R[YT[ R^MUO R]P]VRW',
	    'Po]KkK RRMXM RRVXV Ra[k[ RRERX RREXEXV RbMb[ R]EkEkK RlWk[ RkOaT R]E]Q[X',
	    'PoTE_E RcMkM RSNXN RcTkT RW[[[ RUI[I[[ RcFcV RcFkFkU RaM]Q RWPTX R[J\\P^TaWeYkZ',
	    'PoWJgJ RWNgN RR[l[ RURU[ RWEWN R\\R\\[ RbRb[ RWEgEgN RURiRi[',
	    'PoRMYM RRVYV RRERX RREYEYV R_F[K R^HbMkQ R`C^H R_GjGdMZQ R_ShU R\\Wh[',
	    'PoRNYN RRVYV R_[c[ RRFRX RRFYFYV R]IcIc[ RgTlY R^TYY RkLfQ RcLhV R[N`O\\W RcJfS R^DfF',
	    'PoVHhH RVKhK RRNlN Rd[i[ RTQT[ RVDVK RVDhDhK RTQjQj[ R_SeV R_S\\VWX R_N^T',
	    'PoVHjH RVLjL RRO^O RhXlX RVDVL RXOXX RbOb[ RVDjDjL RbOlOlX R_WRY',
	    'PoVKhK RXOgO RXSfS RXWfW RS[k[ RXNXW RgOgW RVEhEhJ RVEVLSX RgLjSnW',
	    'Po[JlJ RRMYM RRVYV RdZlZ RRERX RREYEYV ReJeZ RlWlZ R]VX[ RbX^Y RiPaX ReEiF RjMgR R_Q[Y R`C`K^U',
	    'PoVJhJ RVOhO RR[l[ RVEVN R\\Q\\[ RbQb[ RVEhEhN RiTeW RVSXX',
	    'Po\\GlG RRNXN RRVXV RaYkY R]YgY RRFRX RRFXFXV RcCcG RhSl[ R\\G\\TY[ RdK_Y R\\N[X',
	    'PoRMXM RRVXV Re[l[ RRERX RREXEXV RfDf[ RlXl[ RgPkT R^RZT RjIgL R_VZ[ R[G]L R`Q\\R R`D`Q_W',
	    'PoUIhI Rb[l[ RbNbZ RUDhDhM RTNXR RUDUMiMeR RlXk[ R[N[VXYS[ RbTiV RZTRW',
	    'Po\\GjG R[LlL RRMYM R[QlQ RRVYV Rb[g[ RRERX RREYEYV RcCcL RgLg[ R]TaX',
	    'PoUHiH RUMiM RRSlS Rb[l[ RUDUL R_M_S RcSc[ RUDiDiL RVNYQ RhNdQ RlXk[ R[SZWXYS[',
	    'PoRMYM R[NlN RRVYV Re[l[ RRERX RREYEYV RcCcN RfNf[ RlWl[ R^VZ[ R\\E_K RiEfK R`N^X',
	    'Po[EkE RRNYN R]PiP RRVYV R]ViV RY[l[ RRERY RREYEYV R]J]V R]JiJiV',
	    'Po\\HkH RRNYN R[NlN R\\TkT RRWYW RY[l[ RRFRY RRFYFYW RcCcN RcPc[',
	    'Po[GkG R_MhM RRMXM R\\QjQ RRUXU Rb[g[ RRERX RREXEXU ReGkM RbG[N RcC`J R`Q^UiUg[',
	    'PoZGlG RRNXN RRVXV RRERX RREXEYV RcCcG RgJkN R_JZO R_O`SeXl[ RgNfSaWY[',
	    'PoTEkE R_LiL RRNlN RVVhV RVZhZ RVQV[ RVQhQh[ RTL]K]M RgIjL RXFVL RcFaL',
	    'Po\\EkE RbMjM R^MgM RRNYN R[NcN R\\TjT RRVYV RZ[l[ RRERY RREYEYV RcOc[ RfIlN RbE^M',
	    'PoTDkD RRMlM RVUhU RVYhY RVPV[ R\\D\\M RbDbM RVPhPh[ RUFWK RhGgK',
	    'PoRMXM RaUgU RRUXU Rh[l[ RRERX RREXEXU R\\H\\[ RaMaV RaMgMgU R\\HlHl[ RdCbH',
	    'Po`IiI RRMYM RaUhU RRVYV Rh[l[ RRERX RREYEYV R]E][ RaNaV RaNhNhU R]ElEl[',
	    'Po[JlJ RRMYM RZSlS RRVYV RRERX RREYEYV R_D_S RgDgS RgVl[ R`VZ[',
	    'PoUHiH RULiL RRSlS RUEUL RUEiEiL RSOSPkOkQ R[RVW R_L^OYS RWWjZ RfScW_YR[',
	    'Po\\GlG RRMYM R]OfO RRVYV RRERX RREYEYV RfVk[ RbJ]O RdCdF RbHaL RhLbR\\T RjPeV[[',
	    'PoSHkH RSLkL RYWeW Rf[l[ RRPR[ RSESL RYSYX RYSeSdW RSEkEkL RRPlPl[',
	    'Po[ElE RRNXN RfRlR RRVXV R\\YlY RRERX RREXEXV R\\J\\[ RaEaP RfEfR R\\JlJl[ R_R]S',
	    'Po`GhG R[HbH RRMYM R[RlR RRUYU Rf[l[ RRERX RREYEYU RfRf[ RlXl[ R_W\\[ RkLkO RbJeNiO R`CbK R`R_Y RiIcL[N RkFeG',
	    'PoRMXM R[TlT RRUXU RRERX RREXEXU R^F^P RcQc[ ReCeOkNlO R_C\\J RjEgI`L',
	    'PoUGhG RUJhJ RTPiP RVTiT RRXlX RRLRO RUDUJ R`R`[ RUDhDhJ RRLkLkO R\\NVT',
	    'Po]KjK RRMXM R\\RkR RRVXV RZVlV RRERX RREXEXV RdMd[ RaH]R R[E[I R[EkElI',
	    'Po_JgJ RRMYM RRUYU R\\ZiZ RRERX RREYEYU R\\T\\[ R\\TiTi[ RcCiIlJ R\\NiNfT RcC\\JYK',
	    'Po]HjH R]MjM RRMYM R\\PkP R[UlU RRUYU RRERX RREYEYU R]D]M RcPc[ R]DjDjM',
	    'PoZIjI RRMXM RRUXU RRERX RREXEXU RgEkJ R`PZV R^RcXk[ R`D^H R`I^MZO RfHeNkMkN R_RiRdW`YX[',
	    'PoRMYM R\\QjQ RRVYV Re[l[ RRERX RREYEYV R]I]Q ReQeZ R[KjJjQ RlWl[ R_FZK R`C^H RaT^XW[ R_FgFcJbU',
	    'Po]IiI RRNYN R]NiN R]SiS RRVYV Re[l[ RRFRX RRFYFYV R]D]R RfSf[ R]DiDiR RlWl[ RaS_X]YY[',
	    'PoUHjH RUKjK RURjR RfZkZ RUEUK R`N`[ RUEjEjK RTNjNjR R[L[N ReLdM RTVlVkZ RVRTW R`UYYR[',
	    'PoRHlH RUKiK RTNjN RRQlQ RVVhV RVYhY RR[l[ RVSVX R_C_Q RVShShX RUEiEiK',
	    'PoZLlL RRMXM RRVXV RgYjY RRERX RREXEXV R^P^Y RdNd[ R\\RjQjX R`MZS RaI^O R\\DkI RiCbGZI',
	    'PoUHiH RTOlO RXY\\Y RUDiDiK RlXk[ R`OcWfZj[ RhRcX\\Z R`McV RUDUKgLiN RTS\\S\\Y RTOTTSZ',
	    'Po[HlH RRNXN RRVXV R]VaV RRFRX RRFXFXV R[NbNaU RgCjF RgT`[ RjMfV RlWk[i[ ReCeLgX R[H[RZY',
	    'Po[GlG RRNYN R\\PkP R\\UkU RRVYV Rg[k[ RRFRX RRFYFYV R\\L\\[ RdCd[ R\\LkLk[ RgDkE',
	    'PoRG]G R_IlI RWVhV RWZhZ RWRW[ RXCXP R_E_L RgIgP RTPWQhRh[ RfD_E RiDaE R]JRL',
	    'PoYIaI RcLlL RRMXM RRUXU RZ[][ RRERX RREXEXU R^C^[ RiLi[ RaPYR RkDcFcSaZ',
	    'Po\\DkD RRMYM R[OlO RRVYV R]ZjZ RRERX RREYEYV R]S][ R]IiIiO R]SjSj[ RaE_O',
	    'Po^LiL RRMYM R[QlQ RRUYU Rf[l[ RRERX RREYEYU R[G[J RcCcG RfQf[ R[GkGkJ RlXl[ R_V\\ZY[ R`Q^X',
	    'Po]GlG RRMXM RRVXV R\\WlW Rc[h[ RRERX RREXEXV R^KjKjW RbRdU RbLdO R^C[L RZQkQh[ R^K\\W',
	    'Po^HkH RRMYM R[NlN RRVYV R]ZjZ RRERX RREYEYV R]R][ RcCcP R]RjRj[ R_D\\K',
	    'PoVHhH RVKhK RUNkN RXQiQ RTTlT RVEVK RZTZZ RVEhEiK R_TcXgZl[ RiVcW RUNTTS[ R`ZXZ RbY[Z',
	    'PoRMYM RRVYV Re[l[ RRERX RREYEYV RdJdQ RfQf[ RlWl[ R`D^G[J R_FgFdI R]JkJkQ]Q]J R`Q_W\\YY[',
	    'PoRMXM R\\NjN RZTlT RRUXU RRFRW RRFXFXU R\\H\\T R`C`T ReCeT R\\HjHjT RfVk[ R_VZ[',
	    'PocFlF RRMXM R\\NjN R\\RjR RRVXV RZVlV RRERX RREXEXV R\\J\\R RcCcJ RcRc[ R\\JjJjR',
	    'Po[GkG RRMXM RZTlT RRVXV RRERX RREXEXV RcCcG RcQc[ R^LaO RgLkP R_I\\O RhIeO',
	    'Po_IiI RRMXM R_MiM RRUXU R`WhW Rh[l[ RRERX RREXEXU R`R`X RdGdP R`RhRhW R]ElEl[ R]E]R[Z',
	    'PoTGjG RRNlN RWUgU RWZgZ RWQW[ R\\G\\N RbGbN RWQhQh[ RVIXL RYCZG RfDdF RhHgL',
	    'PoVHhH RVKhK RRNlN RVUhU R[[_[ RVDVK RVQVU R_K_N R_U_[ RVQhQhU RVDhDhK ReWlZ RYWRZ',
	    'PoZJbJ RdLlL RRMXM RRUXU RRERX RREXEXU R^C^[ RiLi[ R^MbQ R^H[T RlDcEdRbY',
	    'PoRMXM RRUXU RRERX RREXEXU R[Q^T R[F^I RjFfI RjQfU RbIhLkN RbRdVfYl[ RaL[N RaV_YY[ RcCbK RcOaW',
	    'Po\\KbK ReKjK RRMXM R\\QjQ RRUXU Re[l[ RRERX RREXEXU R]E]P RfQf[ RdEjEjP RlWl[ R_V\\YX[ RbD\\E R`Q^X',
	    'PoRG^G R`IlI RWVhV RWZhZ RWRW[ RXCXP R`E`L RgIgP RWRhRh[ RXH[K RWIRN RXFVK RiD_E',
	    'Po\\FlF R\\IkI R[LlL RRMXM R]SjS RRVXV R]VjV Rf[j[ RRERX RREXEXU R]O][ RdCdL R]OjOj[',
	    'PobGlG RTM^M RVVhV RVZhZ RVRV[ RTH^H^L RVRhRh[ RbGcKfNlP RcC`J RiGhKdN_P RTETKSP R]DTE',
	    'PoWIgI RWMgM RRU]U RbUlU RRZ]Z RbZlZ RRPR[ RWDWM RRP]P][ RbPb[ RWDgDgM RbPlPl[',
	    'PoVGiG RVJiJ R^SlS RWZhZ RVEVJ RWUW[ RWUhUh[ RVEiEiJ ReNiQ RdM`Q RYKTP RWN^MZQSU RZQdS',
	    'PoUHhH RUKhK RRN^N RaPlP RXS]S RTRTZ RUDUK RXNXY RUDhDhK RaPbUgYl[ RbM^U RiPfUcX][ R^XRZ',
	    'Po^IkI R^MkM RRMYM RRUYU Rf[j[ RRERW RREYEYU R^D^M R^DkDkM R`NZT RbS[Y RfVa[ RgQdX R^RlQk[',
	    'PoUF_F RRJ`J RbNlN RWUiU RWZiZ RWQW[ RbFbM RWQiQi[ RbFlFlM RUCTH RYL_N RYKVORP RZFYM',
	    'PoZGlG RRNXN R\\QiQ R\\ViV RRVXV Rc[k[ RRFRX RRFXFXV R]L]W RcJc[ R[MjMiV ReHlN RkXk[ RaGZN RbC_J',
	    'PoRMXM RhUkU RRVXV Re[l[ RRERX RREXEXU R[F[J RcCcF ReLe[ ReLkLkT R[FkFkJ RlXl[ R[P`T R_V[[ RaQ]Y R]J[S R]MbM_W',
	    'PoYJaJ RcJlJ RRMXM RRVXV RRERW RREXEXU R^C^[ RgCg[ R^MbQ R\\NXW RgJaV RgHjU R^HZU RgGeQ',
	    'Po[GlG RRMYM R]QjQ RRUYU R_[c[ RRERX RREYEYU R]K]Q RcQc[ R]KjKjQ RhTlX R^UZY RcCcG',
	    'Po^IkI R^MkM RRNYN R]UlU RRVYV R]ZlZ RRFRX RRFYFYV R]Q][ R^E^M R^EkEkM R]QlQl[',
	    'Po`IlI RRMXM R`QkQ RRVXV R_ZlZ RRERX RREXEXV R]L][ ReCeZ R^DZI R]MYP R^J\\O',
	    'Po[GjG RZLlL RRMXM RZRlR RRVXV Re[l[ RRERX RREXEXV RcCcK RfRf[ RgIgR RlXk[ R^I^Q R^R]W[YW[',
	    'PoRE_E RbJlJ RSJ_J RVVhV RVZhZ RVRV[ RYHYQ RhJhQ RVRhRh[ RVFSJ RWCUH RbEbK`P RjDaE RYMRN R]MVN',
	    'PoVIhI RVMhM RSP[P RbPkP RTS[S RbSjS RbWlW RVDVM R\\M\\V RbMb[ RVDhDhM RTW[WYYS[ RXWRW',
	    'Po^KiK RRNXN R]SiS RRWXW R]WiW RZ[l[ RRFRY RRFXFXW R[F[I R]N]W RcCcF R]NiNiW R[FkFkI',
	    'Po_EkE RRMWM RaOkO RRVWV RaVkV R^ZlZ RRERX RREWEWU RYIYO R\\C\\[ R^I^M RaIaU RaIkIkU',
	    'PobHlH RYHaH RRMWM RRUWU Rc[l[ RRERX RREWEWU R[M_M_Y RdLdZ RgJgW RlXl[ R\\C]F RhNaQ RkMcP RcCaK RkMkUhU R[[^[[[ R[H[PYY',
	    'Po[KbK RdKkK RRMXM R[PaP RRUXU R[UbU RRERX RREXEXU R[E[[ R[EbEbK RdEkEkK RcOkOhVeZb[ RdPgWk[',
	    'PoUGiG RUIiI RUNiN RVShS RVVhV RRYlY RSLSO RUDUI RVQVV R_L_[ RVQhQhV RUDiDiI RSLkLkO',
	    'Po\\IkI RRMXM R\\PjP R\\TjT RRUXU RZWlW RRERX RREXEXU R[D[G R\\L\\S RcGc[ R\\LjLjS R[DkDkG',
	    'Po[ElE R]MjM RRMYM R]RjR RRVYV R]VjV RRERX RREYEYV R]I]V R]IjIjV RfXl[ RcEbI R`XY[',
	    'PobGlG RTH^H RTK_K RVVhV RVZhZ RTDTP RVRV[ RTD^E^H R_N^P RVRhRh[ RbGdKgOlQ RcC`J RiFgLdO`P RYH[O RYORP',
	    'PoRMXM R]NhN RZSkS RRUXU RRERW RREXEXU RfHkN RhEfH RlHgJ RZH_IZN RcReX RdDgK RcNcUgYk[ R\\EaE^K RbU_YZ[',
	    'Po]HkH R]MkM RRMYM RRVYV R\\XhX Rg[k[ RRERX RREYEYV R]R]Z R]D]M R]DkDkM R`NZT RcR_V RcTgV R^QlPlUk[',
	    'PoZFkF RRMXM RZSlS RRVXV RRERX RREXEXV R]L]S R_C_I RfCfI R]LiLiS RcIcTeWhYl[ RbU^YZ[ RcNbV',
	    'Po\\GhG R[LlL RRMYM R_UjU RRVYV R_ZjZ RRERX RREYEYV R`P`[ RbCbL R]QjQj[ RkFcNZR',
	    'Po[JlJ RRMYM R]PiP R]SkS RRVYV RZWlW RRFRX RRFYFYU R]L]P ReQe[ R\\FiFiJ R]LiLiP RaC`J R]S]W',
	    'PoVHiH RVKiK RRQlQ RXWiW RXZiZ RVDVK RYSY[ R^L^Q RVDiDiK RVTiTi[ RkLSU RVNeN',
	    'Po]IjI R]MjM RRNYN R]RkR R]VkV RRVYV R[[l[ RRERX RREYEYV R]E]M RdOd[ R]EjEjM R^O\\T',
	    'PobGlG RWVhV RWZhZ RWRW[ RZIZP RWPXQhRh[ RWMRO RYITN RRJ^I]L RbGdJgNlP RVF\\I RcCaI RiGhKeN_P RTE^EYG',
	    'Po[FlF RRMXM R^QiQ R^UiU RRVXV Rh[l[ RRERX RREXEXV R[K[[ RcCcK RcQcZ R[KlKl[ R_M`O RgLfO',
	    'Po[GkG RRMYM R[RjR RRVYV RRFRX RRFYFYV R[J[R RcCc[ R[JjJjR RcQiXlY R^M_P RgLeP RbQ]WXY',
	    'Po[KkK RRMXM RZNlN R^RiR RRVXV RRERX RREXEXU R[EcEbI R\\VX[ R]F]I R^RcXk[ R^Q[Y RiFgJ RhSdX][ R_K]V RiCbD',
	    'Po[FkF RZMlM RRMYM R]UjU RRVYV R]ZjZ RRERX RREYEYV R]Q][ RcDcF R]QjQj[ R_H`L RhHgL',
	    'Po]HiH R]LiL RRMXM RZOlO RRUXU Rf[i[ RRERW RREXEXU R]D]L R]DiDiL R^QZV RaT[[ RfScZ R]SkSj[',
	    'Po[FkF R\\JjJ RRMXM RZNlN RRVXV R^VhV R^ZhZ RRERX RREXEXV R^P^[ R]RiQh[ RgOlT R_OZT ReJhR RbH]R RcC_O',
	    'PoRMXM R^PjP RZTlT RRVXV R]ZjZ RRERX RREXEXU R]V][ RcOcZ RjVj[ R\\HcK R_FfI R_M]R R`FiEeJ\\N RbC_GZI',
	    'Po[GkG R\\KjK RRNYN R[OlO R`TjT RRVYV R]ZlZ RRFRX RRFYFYV RcGcO ReTeZ R^D_F RhCfG R^R[Z R^O\\X',
	    'PoRF]F R`FkF R_NlN RVVhV RVZhZ RVRV[ RXCXF ReCeF RVRhRh[ RUHVM RbHcM R[HYN RhHhM R]MRO',
	    'Po]MiM RRMXM R]QiQ R[TlT RRVXV RRERX RREXEXV R[E[H R]I]Q R]IiIiQ R[EkEkH RiXl[ RfWjY R^YY[ RcQcU',
	    'Po]IiI R]MiM RRMYM R]PiP R[SlS RRVYV RRERX RREYEYV R]F]P RbCbF RcPc[ R]FiFiP RcShXlZ RcS^XYZ',
	    'Po[JkJ RZMlM RRNXN R\\UjU RRVXV RY[l[ RRERY RRFXEXV R[E[I R\\P\\U RcCcI R\\PjPjU RjEjI R_W_Z RgVfZ',
	    'PoUGiG RUIiI RRLlL RWQgQ RYYeY Rf[k[ RSTS[ RUDUI RWNWQ RYVYZ R_I_L RYVeVeY RWNgNgQ RUDiDiI RSTkTk[',
	    'PoaLiL R]MfM RRMXM RRVXV R^[a[ Rg[k[ RRERX RREXEXU RZPaPb[ RdPkPk[ R\\R^T ReRhT RfIjM R`D[I RbG\\M ReDhGlI RaUZX RkUcX',
	    'Po_HjH RRL[L R_LjL R[OlO RRQ[Q Rf[j[ RRGRR RWCW[ RRG[G[Q R_D_L R_DjDjL RaO[U RbS\\Z RfVa[ RgRdY R^SlSj[',
	    'Po^GkG R_JiJ RRMXM R[TfT RRVXV RRERX RREXEXV RaOa[ RaT[Z RdOcS R]O]S RaVeX R_C\\J RlXk[l[ R[MiMiUiZ',
	    'Po]IiI R]MiM RRMXM RZQlQ RRVXV R]WiW RRERX RREXEXV R]F]M RcSc[ R]FiFiM RfQjU RbO[V RcCaE',
	    'Po]HjH R]KjK RRMXM R[SlS RRVXV RRERX RREXEXV R[N[Q R]D]K R]DjDjK R[NlNlQ RaP`T^VjZ RgTcY[[',
	    'PoUElE RWJ_J RaJkJ RYViV RYZiZ RYRY[ R[G[P RfGfP RYRiRi[ RZJ_N RgKlO ReK`O RZKVO R[HYN RUEUQSZ',
	    'PoRMXM R[OkO R_RiR RRUXU RRERW RREXEXU R[EcEbI R[K[LkKkM R]WY[ R]F]I R^RcXk[ R_Q[Y RiFgJ RiSdX][ R`M]W RiCbD',
	    'Po`EkE RTG]G R`JlJ RTJ]J RVUhU RVYhY RR[l[ RTDTP RVRVY RTD]D]J RaF`J RVRhRhY RgKfPlOlP ReLaP RfEdM R[L]ORP',
	    'PodFlF RRMXM R^OhO R[SkS RRVXV RRERX RREXEXU R[K_L RhVkY RfKkP RkHgK R]VZY R]FYJ R^HaJ[P RdDgM R]FbF_M RcSc[_[',
	    'Po[FlF R_JhJ RRMXM RRVXV Rh[k[ RRERX RREXEXV R[M[[ R_C_J RcJc[ RhChJ R[MkMk[ RePhPiV RhQeW R]P`P`V R_Q]W',
	    'PoRE_E RaJlJ RSJ^J RSL^L RRN_N RVVhV RVZhZ RSGSL RVRV[ RYCYQ RSG^G^L RhJhP RVRhRh[ RaEaL_P RaG`O RjDaE',
	    'PoTE]E RRH_H RZK^K RSM]M RVVhV RVZhZ RVRV[ RXKXP RXCYHZK RRQXQZQhRh[ RaGiGjP RlNlR RVITK R`JeL RaO_P RdDcN',
	    'Po\\JjJ RRMXM R\\NjN R\\Q`Q RfQjQ R[T`T RfTkT RRVXV RZWlW RRERX RREXEXV R\\F\\N RbCbF RcOc[ R\\FjFjN',
	    'PoRElE RVKhK RRQlQ RXWgW RXZgZ RXSX[ RZCZG RdCdG RVThTg[ RdRlU R\\NXTRV RVHhHhOVOVH',
	    'Po[FbF RdFlF RRMXM R\\MjM R\\QjQ RZUlU RRUXU RRERX RREXEXU R\\J\\Q R_C_H RgCgH R\\JjJjQ RdWlZ RcRaW_YZ[',
	    'PoRMXM ReSkS RRUXU RRERW RREXEXU R]L][ RbWeZ RbGeJ RhHkK RaTcY R]C[H R^I[Q RiEeL RcD`L RaP_Z RfNeZk[',
	    'Po[ElE R^IkI RRMXM R^NkN RRUXU RaWjW R[ZlZ RRERX RREXEXU R[E[[ RbPbV RgGgK R`RjQjV RbP^T RaGbK`R',
	    'Po[FkF RZJlJ RRMXM R\\PjP R\\SjS RRVXV RZWlW RRERX RREXEXV R\\M\\S R_F_J RcCcF RcSc[ R\\MjMjS RhFfJ',
	    'PoZFbF RdFlF R[IbI RdIkI RRMXM RdMlM RRUXU RYVlV R\\ZjZ RRERX RREXEXU R^C^O RhChP R\\QjQj[ RbLZM',
	    'PoVGhG RVKhK RTNjN RRRlR R[[_[ RVDVK RZKZR R_T_[ RVDhDiK RYU[V ReTcV ReKeSlV RYSRV RaXiZ R]XUZ',
	    'PoZFlF R_IgI RRMXM R\\PjP R\\SkS RRVXV RZVlV RRERX RREXEXV R\\L\\P R_C_I RgCgI R\\LjLjP RcIcVfYl[ RbV_YY[',
	    'PocHlH RYHaH RRMWM RgRkR RRUWU RRERX RREWEXU RbLkLkP R\\D^F RcTeY RdCbK R_[\\[ RgLgZl[ R\\H\\PZY R\\M`M`U_[ RcPbZ',
	    'PoZFlF R\\KkK RRMXM R\\NkN RZUlU RRVXV Rb[f[ RRERX RREXEXV R\\H\\N RcCcQ RfSf[ R\\HkHkN R]W`Y R[RiQkR',
	    'PoZFlF RRMXM R]PhP R]ThT RRVXV R_[b[ RRERX RREXEXV R^L^T RbTb[ R\\MiLhT RhWkZ R[I^K RdGiLlN RkIhK R]VZZ RaHZN RbC_K',
	    'Po\\GjG R\\JjJ R\\NkN R\\QjQ R\\TjT R\\WlW R[[l[ R\\D\\J R]M]X RdMdW R\\DjDjJ RRPVPVWR[ R^KYQ RSDWF RRJWL RYZa[ RdKdM',
	    'Po[IbI ReIlI RRMYM R\\OkO R\\SkS RRUYU RZWlW RRERX RREYEYU R[D[I R\\L\\S R[DbDbI RcLc[ ReDeI R\\LkLkS ReDlDlI',
	    'Po]HjH R]KjK RRMXM RZNlN R]UiU RRVXV R`[c[ RRERX RREXEXV R]D]K R]Q]U RcUc[ RcKcN R]QiQiU R]DjDjK RhWkZ R^W[Z',
	    'PoWVhV RWZhZ RTFTP RWRW[ RYCYP RWRhRh[ R\\D_G R[J\\M RbFdKhNlP RVJVN RcCaI RbGjGhKeN`P RVDRH^H^P\\O',
	    'Po[HkH RRMXM RdQlQ RRUXU RdWlW RRERX RREXEXU RcCcN RdSdW RhOh[ R]D_G R]U`V[[ RiDfG RcHfKlM R^O[T R]RbQ_X RcH`KZM',
	    'PoYGcG RRMXM R[MbM RRVXV R[[^[ RRERX RREXEXV R[I[M R^S^[ R^C^G R[IbIbM RZPbP^T RhVb[ RfRjZ RjPeY RfFeLhW ReIkIhV RfCdO RcTYV',
	    'PoZFlF R\\IjI RRMXM R]OiO RZSlS RRVXV R\\[i[ RRERX RREXEXV R\\V\\[ R^P_S RcCcI R]KiKiO R\\ViVi[ RgPfT R]K]O',
	    'Po[FkF R\\IjI RRNXN R]OhO R\\ViV RRWXW RY[l[ RRERY RREXEXV RZLZO R\\R\\V RcCcI R\\RiRiV RZLkLkO R_X_[ RfXe[',
	    'Po`EkE RSH]H R`KlK RSJ]J RSP]P RVUhU RVYhY RR[l[ RSESJ RTLTP RVRVY RSE]E]J R`G_K RVRhRhY RfPlOlP ReLbO_P RfEeLfP R\\LSM',
	    'Po[FlF R_JgJ RZLlL RRMXM R\\SjS RRUXU R\\WjW RRERX RREXEXU R\\O\\W R_C_I RcLcW RgCgI R\\OjOjW RgXl[ R`XZ[',
	    'Po_JlJ RRMWM R`MiM RRVWV R`[l[ RRERX RREWEWV R`FiFiM R`ObR RZK\\M RgSkW R[D]G RYQ]Q\\XY[ RjOgR R\\Xb[ RcT_V ReCeXbX',
	    'PoZFlF R[JkJ RRMXM RZNlN R[SlS RRVXV R[WkW RRERX RREXEXV R^J^S R_C_H RcJc[ RfCfH RhJhS',
	    'PoUGhG RUJhJ RRLkL RTOjO RSRkR RTUjU RRXlX RUDUJ RYOYU RZKZN R_O_[ RcKcN ReOeU RUDhDhJ',
	    'PoUElE RWK_K RaKkK RYViV RYZiZ RYSY[ R\\H\\Q RfHfQ RYSiSi[ R[K_N RfJlP RfJ`P R\\KWP R_GWH RUEUQSZ RiGbH',
	    'PoUGhG RUIhI RTKjK RVP\\P RbPhP RUR\\R RbRiR RUTiT RRWlW RTZiZ RSNSQ RUDUI R_K_R RUDhDhI RSNkNkQ R[WXZ RcXk[',
	    'Po[FkF RZKlK RRMXM R\\QjQ R\\TjT RRVXV R[WkW RY[l[ RRERX RREXEXV R\\N\\T RcCcF RcNc[ R\\NjNjT RgGfJ R_G_J',
	    'Po\\FjF RZIlI RRMXM R[MaM RdMkM RcQlQ RZTlT RRUXU Re[l[ RRERX RREXEXU R^K^Q RbCbI RfTf[ RgKgQ RlXl[ R_T]YX[ RaPZQ',
	    'Po\\HkH RRMXM R]QjQ R]TjT RRUXU RRERX RREXEXU ReCeM R[OjOjW R\\EjEkH RaC`J]N]WhXl[ R\\H[KkKkMhM R`YZ[',
	    'PoZDlD R[LkL RRMXM R\\QjQ R\\TjT RRVXV RYWlW RRERX RREXEXV R[G[K R\\N\\T R`D`K RcTc[ RfDfK R\\NjNjT R[GkGkK',
	    'PoaGjG RTH\\H R`KjK RTL\\L RgNjN RWWgW RR[l[ RTETM RTE\\E\\L R_M_P R_W_[ RaDjDjM RTPTQjPjR RaDaK]N R[ScReSkV RZTRV',
	    'Po\\HiH R\\KiK RRMXM R\\NiN RRUXU RZWlW RRERX RREXEXU R\\F\\N RbCbF RcOc[ R\\FiFiN R\\PaQ ReSkT RjQeQ RaS[U',
	    'Po\\IkI R\\LjL RRMXM RZPlP R[TkT RRUXU R\\WkW RRERX RREXEXU R\\D\\I RaDaI RcIcO RcTc[ RfDfI R\\DkDkI RgQfT R_Q`T',
	    'PoVGiG RVJiJ R\\PcP R\\ScS RVDVJ R\\M\\S R\\McMcS R\\T\\ZcYb[ RfMf[ RVDiDiJ RiRlU RXVT[ RfMlMjQ RXOWS RVLTOWRSU RYSWY RbU\\W RkXhX RYURV',
	    'PoXGgG RXJgJ RSN]N RaNkN RSQ]Q RaQkQ RUUjU RUXiX RR[l[ RSLSQ RXDXJ RSL]L]Q R_R_[ RaLaQ RXDgDgJ RaLkLkQ RWRSV',
	    'PoXFfF RXIfI RTM]M RaMjM RaOjO RXDXH RaJaO RXDfDfH RaJkJkO RTKTO_OdRlT RTK]K^OYRRS R`RWU RcTWX RhV_YU[',
	    'PoZJaJ RRMWM RZNaN RYQbQ RRUWU RRERX RREWEWU RZFZM R]C]F RZFaFaM RhWl[ RgWc[ RfQjZ RiPeY RZZX[ R`[][ RdKhW RjKgW ReJkIhU R]O]Q[Y R\\UaU`[ ReCcN',
	    'PoVGiG RVJiJ RWLhL RUOiO RRRlR RWUgU RZYdY RVDVI R_S_[ RVDiDiI R_UiZ R_UUZ RaRdSlU R_K_O\\QXSRU',
	    'PoYFcF RRNWN RZU_U RRVWV RRFRX RRFWFWV RZOZW RZO_O_U R`C`I RgVb[ RgRjZ RiPeY R\\C\\HXP ReLiX RjKgW Ra[][ ReJhV ReIkIhU RfCdO RZLbLbUa[',
	    'Po[IlI RRMXM R^OlO R_QjQ R_TjT RRVXV R_ZkZ RRERX RREXEXU R_V_[ R_VkVk[ ReLeO R^EfEdH RbJ_L RgJkL R_D[I\\R[Z',
	    'PoZEbE RRMXM R`QfQ RRUXU RRERW RREXEXU RZJZM RZJlJlM R\\F]I RaLdM RhMkP R\\NZP R_M`Q RbFbI RiFgJ R_TYV R^TaXdYk[ R^ThTeWaYZ[ RdD_E RiDbD',
	    'Po[HlH R[MkM RRMXM R\\QjQ R[TlT RRVXV RRERX RREXEXV RaCaH RcMc[ ReCeH R_I_L R\\D^F RjDgG RgIfL RdTgXlZ RcT_XYZ',
	    'Po[DkD R]JaJ ReJiJ R\\MaM ReMjM RRMXM RZPlP RRVXV Rh[k[ RRERX RREXEXV RZGZJ R[T[[ R`T`[ RcDcN RfTf[ R[TkTk[ RZGlGlJ RbP`U',
	    'Po[IkI R\\LgL RRMXM RZPlP RRUXU R^VjV R^ZjZ RRERX RREXEXU R[D[I R_S_[ R`D`I RaJaP ReDfI R]TjSj[ R[DkDkI RiJdPZU',
	    'PoZFlF R]MiM RRMXM R[PjP RRUXU R_[c[ RRERX RREXEXU R_C_H RfCfH R[J[KkJkL ReTjY RdWd[ RdPfV RcP[T RbT[W RkRfT R`RcTdW[Z',
	    'PoYGlG RRMXM R[PjP R[RkR RRUXU RYVlV RRERX RREXEXU R[J[P R`W`[ R[EcEcU R[JjJjP RhWl[ RdWe[ R_L_N R[XX[ RfLfN RiCbD',
	    'Po\\HbH ReHkH R[LbL RdLkL RRMXM R]PkP R]SjS RRVXV R]VjV R]ZlZ RRERX RREXEXV R^N^[ R[DbDbL ReNeZ RdDkDkL R^NYS R^L]R',
	    'Po[GiG RRMXM R[MjM RYQlQ RRUXU R^[b[ RRERW RREXEXT RbSb[ R[DiDiJ R]S_V R[D[JhJgQjU RgTeV ReWjZ R_J_Q\\TXV R`XYY',
	    'Po[DlD RfHlH R^HdH RRMXM R_NjN R_QjQ RRVXV R`XiX Rh[k[ RRERX RREXEXU R^S^[ R_K_Q RaFaJ ReKeX RhFhJ R_KjKjQ R]TkTk[ RgViY R[D[SZ[',
	    'PoXHfH RTN^N RaNjN RUPkP RXShS RUVlV RZUZZ RXDfDfH RiVdX RgLjL RZL^L R`UbXeYk[ RcFgF RTJ^J^MZL RXDXG[FXF RaJaMeL RXLTKTMXL RaJjJjMfL R[F^FaF RUQTY RcF`F]F RaYXZ',
	    'Po[FlF R[IlI R`LhL RRMXM R[OlO R^TjT RRVXV R^WjW RRERX RREXEXV R^Q^W R`F`L RcCcF RdOdW RhFhL R^QjQjW RgYl[ RaY\\[ R[F[RZY',
	    'PoWFhF RWIhI RSM]M RaMkM RSO]O RaOkO RXVgV RXYgY RR[l[ RSKSO RWDWH RXTX[ RSK]K]O RaKaO RXTgTg[ RWDhDhH RaKkKkO RSRSSkRkT',
	    'Po\\IjI RRMWM R^RiR RRVWV R`[c[ RRERX RREWEWV R[F[I RcUc[ R]OjNiU R[FkFkI R\\K^M^UhVkZ RbDdF RkKhN R_W\\Z RdIhNlO RbI^NZO',
	    'PoZFlF R_IfI RRMXM RaTeT RhTlT RRVXV RRERX RREXEXV R]K]T R_C_I RaMaS R`Wa[ RaMeMeS RfCfI RiKiT RlRlT RhWlZ R\\W[Z RkMhP ReWf[ R]QZS RZN^N',
	    'Po\\DkD R]JaJ RfJjJ RfMkM R\\MaM RRMXM R\\RlR R\\TkT RRUXU R\\WkW R\\ZlZ RRERX RREXEXU R[G[J R]P][ RcDcM RdQdZ R[GlGlJ R^OZS',
	    'PocEjE RaJhJ R_KfK RbMjM RRMXM R^QjQ R^TjT RRVXV R[[l[ RRERX RREXEXV R^V^[ R^O^T RbVb[ RcHcM RdOdT RfVf[ R^OjOjT R^VjVj[ R[HlHkJ RcCcFbJ R[H[RZY',
	    'PoZFjF R[IjI RZLlL RRMXM RZNbN RYPlP RZUcU RRVXV R][`[ RRERX RREXEXV R_N_T R\\U[XaX`[ RbFbL RlXl[ R]C_E RjTc[ RgCfF RgMjN R\\PbS RhZk[ R_P\\RYS RdMfX',
	    'PoUHiH RUKiK RRM`M RTQ^Q RaUkU RUW\\W ReZkZ RaZhZ R\\[_[ RRTR[ RUDUK RXWY[ RTOTP]O^Q RRT_T_[ RaOaU RfLfZ RUDiDiK RaOkOkU R[UXX RjWl[ RWUWW',
	    'PodFlF RYFbF RYLcL RRMWM RdMkM RdPkP R[RaR RdSkS RRUWU R[VaV RdWkW RRERX RREWEWU R[O[[ R[H\\K R^C^G RdCdIkIkM ReMe[lZl[ R`H`K R[OaOa[^[',
	    'PoVFiF RVIiI RRKlK RUO]O RaOiO RTQkQ RUTiT RRVlV RVDVI RYXY[ RZOZV RUMUN]M]O R_I_K RdOdV RVDiDiI RaMaNiMiO R`WeYl[ RiWcX RaVRY RbZ\\ZV[ R`ZY[',
	    'Po[IkI RRMWM RcOlO RcRkR RYTaT RRUWU RcVkV RcZlZ RRERW RREWEWU R[E[I R]S][ R`E`I RdMd[ ReEeI RhMhZ R[EkEkI R`QaT RZN]PZS R\\K[O R_M^P R`VaY RZVYZ RdKbQ',
	    'Po\\DbD R[FcF R\\IbI R\\KbK RRLZL RdLlL R\\QbQ RVWhW RVZhZ RVTV[ RVKVR RhKhR RVThTh[ RXJZL RRFUI RdFgI RjFdK RUCTG RXGSK RgCfG RYMYP RkMlQ RSMSR ReMdQ R\\MbMbR R\\M[R',
	    'PoZDaD RdDlD RZMlM RRNXN RZTkT RRVXV R]WbW RZ[b[ RRERX RREXEXV R[G[K R]T][ R`M`S R[GaGaK RcKcM RdGkGkK RZPkPkT RfMe[lZl[ RkVeW ReGdK R^H]J RhHgJ RZMZSYZ',
	    'Po]LhL RRMWM R[RjR R[TjT RRUWU RYWlW RRERW RREWEWU RZGZJ R[N[R R]I]L R`Y`[ RbCbG RbNbW R]IhIhL R[NjNjR RZGkGlJ RiXl[ ReYe[ R]D]F R[YY[ RfOfQ RhDgF',
	    'Po[HbH RdHkH R[KlK R\\MdM RRMWM R]SbS RRUWU R]VbV RRERW RREWEWU R]P]Y R`M`P R[PcPb[ RdDkDkH RhWl[ RhWd[ RfQiY RjRgY R[DaDaH RfPkPhX R[D[H RdDdH RfLeT R[KZSYZ RbX\\Y',
	    'Po[GlG R^KbK RgKkK RRMXM R^PkP RRUXU R_WhW R]ZiZ RRERW RREXEXU R^M^P R_T^W RcRcY RdIdL RfMgP R_ThThW R^MkMkP R[DlDlG RbMbP\\U Rk[h[ RiIkI R_IbI R`I^I RiIgI R_SlSk[ R[D[QZX',
	    'PoRPgP RRYlY RRFR[ RRFlFl[',
	    'PoRLlL RRRlR RRERT R_L_[ RRElElT',
	    'PoRQlQ RRYlY RRIR[ R[D[Y RcDcY RRIlIl[',
	    'PoTLiL RTQiQ RTFTS RTFiFiQ RlWk[ R_C_M`TbWeZj[ RgT`X[YR[ R^G`U',
	    'PoREkE RUMiM RURiR RUIUR RUIiIiR RWS[W_Zk[ R_E_R^U[XS[',
	    'PoTLgL RTQiQ RTGTS RTGiGiQ RbDgF RlWk[ R_QbWeZj[ RgT`X[YR[ R_C^J`T',
	    'PoeOjO RWUjU RWZjZ RWQW[ RWQjQj[ RZCTJ R_FZKUM RfG`M\\O RXGlFkO',
	    'PoWIjI RWMjM RWYfY Re[j[ RWEWM RWRWZ RWEjEjM R`R[V RVRRT R[MTS R_TeV RXQlQk[',
	    'PoRHlH RUKhK RTNjN RRQlQ RVWhW RVZhZ RVTV[ R_C_Q RUEhEhK RVThTh[',
	    'PoRFlF RTLjL RTPjP RVVhV RVZhZ RTITP RVSV[ R[C[P RcCcP RVShSh[ RTIjIjP',
	    'PoRElE RTLjL RTPjP RVVhV RVZhZ RTITO RVRV[ R_C_O RVRhRh[ RTIjIjO',
	    'PoVHiH RVMiM R`QkQ R]WlW RVDVM R`Q`W RfNf[ RVDiDiM RXORT RUSZU RXMWQ^P[UXYS[',
	    'PoVGhG RVKhK RTQjQ RRThT RVDVJ RcMcQ RVDhDhJ RTMjMjQ R[M[Q RTMTQ R\\Xj[ RgUbX^YU[ RXU]XbZl[ RaYR[',
	    'PoRKlK RROlO RUViV RUZiZ RRFRO RURU[ R_F_O RURiRi[ RRFlFlO RVCYF RhCeF',
	    'PoVOhO RWVgV RWZgZ RVHVO RWRW[ R_I_O RWRgRg[ RUJiIhO RYK[N RdKcN RYETJ R\\EdDhIlJ',
	    'PoSF]F R`FjF RRJ]J R`JkJ RWUgU RWZgZ RWQW[ RWQgQg[ RXCXKSP RWL\\N ReDeJaO ReJkO',
	    'PoVHhH RVKhK RRNkN R_RjR RUR]R RUU]U RUNUY RVDVK R]N][ RVDhDhK RbTfYl[ RjSeX^[ R\\XSY',
	    'PoTF^F RbFkF RRJ^J R`JlJ RVVhV RVZhZ RTFTJ RVRV[ RZJZO RbFbJ RVRhRh[ ReJfPkOkP ReLaP RXLTP R^OYO RZCXM RfCeM',
	    'PoVHiH RVKiK RRNlN RUR]R RUU]U RfZjZ RUNUY RVDVK R]N][ RVDiDiK RdW`[ RePcX R`RkRjZ R]XRY R`WVY',
	    'PoZHdH RVQhQ RXWgW RXZgZ RVKVQ RXSX[ R_K_Q RXSgSg[ RVKiKiQ RYM[O RdMcO RaElJ R^C[FRJ R^D`D',
	    'Po^FlF RRM[M R^RkR RRR[R RRHRS RWCW[ RRH[H\\R R^J^R RaLaP RdCd[ R^JkJkR RfTlZ RdPcT]Z RdPfShV RhLgP',
	    'PoSH\\H R_HkH R_LkL RRN]N RRXYX R_XiX Rg[k[ RZR]Y R_D_L R_Q_Y R_DkDkL RaO\\T ReSaV RdThV RXCXMTX RaM`QlPlVk[ R\\WUX',
	    'PoRO^O RSU]U RSZ]Z Re[i[ RRGRN RSQS[ RXGXN RSQ]Q][ RRG^G^N RcP_T ReTaY R\\CZG RTIUL RUCUF RhMdW R[I[L RdD`M RfIaS RiIfU RbIlIlQjZ',
	    'PoVI]I RaKkK RaOkO RTP^P R`UlU RUW^W RUZ^Z RTKTP RUSU[ RYKYP RUS^S^[ RTK^K^P RaFaO RhQh[ RaFkFkO RZC^H RfFfObT RYCRJ RVMVO R\\M[O',
	    'PoYLlL RXSlS Rd[l[ RYElEl[ RYEYNWTUWS[',
	    'PoRGlG RYQhQ RYVhV RYLY[ R]CYLSR RYLhLh[c[',
	    'Po^EkE RTK[K R\\MlM RTR[R RW[Z[ Rg[l[ RTD[D[[ RgMg[ RlWl[ R^Z\\[ RTDTSS[ RaN`X',
	    'PoUK^K RcKlK RUR^R RcRlR RY[^[ Rg[l[ RUE^E^Z RcElEl[ RUEURTZ RcEcRaY',
	    'PoTKZK RTRZR Rd[h[ RW[Z[ RTEZEZ[ R`W\\[ RaEgEkM R`G]M RbO`X R^OjOiTh[ RTETSS[',
	    'PofJjJ RTK[K RTR[R RW[[[ RTD[D[[ R`D`[ R`DjDjJ RTDTSS[ R`NkNgWb[ RbNfWk[',
	    'PoTKZK R_LhL RTRZR ReXjX RV[Z[ RTDZDZ[ RbPb[ R]PjPjX RdDlL RbE\\L RTDTSS[',
	    'PoTK[K R_NkN RTR[R R^YlY RW[[[ RTD[D[[ R^Q^[ R_F_N ReCeY RkFkN RlQl[ RTDTSS[',
	    'PoTKZK RTRZR R_TfT RV[Z[ Re[j[ RTEZE[[ R_L_V R_LfLfT RaC]K R_HlHlSj[ RTETSS[',
	    'PoTKZK R\\KlK RTRZR RcTiT R^ZlZ RV[Z[ RTDZDZ[ R^D^Z RcCcT RiDiT RTDTSS[',
	    'PoTK[K RTR[R Rh[l[ RW[Z[ RTD[D[[ R^H^[ R^HlHl[ ReQjV ReIjN RdK`O RdRaV ReOcU ReEcL RTDTSS[',
	    'PoTKZK RTRZR Rf[l[ RV[Z[ RTEZEZ[ RfCfZ RlWl[ RgPkT RkHgL R]H_L RaQ\\S R]Z\\[ RTETSS[ RaCaR`X',
	    'PoRI`I RcKlK RbRlR RTS^S Rg[l[ RTLTU R^L^T RcElEl[ RXWT[ RTCVG R]C\\G RcEbT_Z RYIYSXX RcNaX',
	    'Po^IkI RTKZK R]PlP RTRZR R_DaH RiCfI RTDZDZ[W[ RTDTSS[ RdIdPaV][ RdPhWl[',
	    'Po_KjK RTKZK R_PjP RTRZR RV[Z[ RTDZDZ[ R_F_Z RdCdF R_FjFjO ReUk[ RiTfU RjRhT RcPgX RfY]Z RTDTSS[',
	    'PoTK_K RcKlK RTP_P RbRlR Rg[l[ RTGTY RYCYG RTG_G_P RcElEl[ RcEcS_Z RZS^WRY',
	    'PoaIjI RTKZK RTRZR RV[Z[ RTDZDZ[ R^IgJkO RgEkJ RbO\\U RcC]I RaK]O R_SdXk[ R_RiRdW`Y[Z RTDTSR[',
	    'PocKlK RZL_L RcRlR RRU_U Rg[l[ RRFRX RVOVU R[F[L RVO[O[U RRF_F_W RcElEl[ RWFVL RcEcRaY',
	    'PoRFlF RVKjK R`QjQ RSR\\R R`VjV RSW\\W Rf[j[ RSNSX RVFVK RSN\\N\\W R`NjNj[ R^C`F R`N`W[[',
	    'PoRG`G RUN^N RUSjS RVWiW RR[l[ RUGUN RYCYG R_S_[ RbEbKaO RbEkEkPgP RbHkH RaLkL',
	    'Po\\GlG RTKZK R^NjN RTRZR R\\VlV RW[Z[ R`[d[ RTDZDZ[ R^J^N RdCdG RdTd[ R^JjJjN R^QjQdT RTDTSS[',
	    'PoRG_G RcKlK RSN^N RbRlR RSR^R RRV`V Rh[l[ RSJSR RYCYJ RYRY[ RSJ^J^R RcElEl[ RcEbRaX',
	    'PoSFjF RXIeI RXKeK RRNlN RXTeT RXXeX Ra[e[ RXQfQf[ RXQXWT[ RfCeNhQlS RXCYNVRRS',
	    'PoRG`G RcKlK RUK^K RUP^P RcRlR RRT`T Rh[l[ RUDUT R^D^T RcElEl[ R[W^Y RVWR[ RcEcRaZ',
	    'Po[FkF RTKZK RTRZR R[SlS RV[Z[ RTDZDZ[ R^L^S R`C`I RgCgI R^LiLiS RcIcSeWhYl[ RbU_Y[[ RTDTSS[ RcNbV',
	    'PoTKZK R]LjL RTRZR RV[Z[ RTDZDZ[ R]E]K RkEkK RhNkQ ReGhJ RaM]Q RbO]U RcH_J RgDbI R`SdXl[ R`EdF R`SiSeW][ RTDTSR[',
	    'PoSE^E RaHkH RaMkM RSL^L RSP^P RgPkP RTTbT RTWjW RR[l[ RSESP R_S_[ RaEkEkP RXLYP RXEYH RSH^H^M RaEaK`P RhRbS',
	    'Po]FkF R\\KlK RTKZK R^PkP RTRZR R^TkT R]WkW R\\ZlZ RV[Z[ RTDZDZ[ R^M^T R`G`K RdCdF RdMdZ R^MkMkT RhFgK RTDTSS[',
	    'PoUG^G RcGlG RUJ^J RcJlJ RZN^N RhNlN RUS^S RcSlS RUW^W RcWlW RZ[^[ Rh[l[ RUDUK RUD^D^M RUP^P^[ RcDcK RcPcX RcDlDlM RcPlPl[ RUPUUTY',
	    'PoTKYK RTRYR Rd[i[ RV[Y[ RTDYDY[ R\\N\\Q R\\NkNlQ R_HbK RhHlL RkEjH R\\E\\I RbEbH ReEeH R^K[L RfKdL RbV_Z\\[ R\\SjSi[ R`D_J RcPbX RhDgJ RTDTSR[',
	    'Po[FlF RTKYK R_LiL R\\OlO RTRYR RV[Y[ RTDYDY[ R\\I\\L RaCaH RgCgH R\\IkIkL RfTkY RjSfT RdOgV RdO\\S RcS\\V RaQdUbW\\Z RdSeX Rd[`[ RTDTSR[',
	    'PoeFlF RZFcF RTKXK RZLcL ReMkM RePkP R[RbR RTRXR ReSkS R[UbU ReWkW RU[X[ R_[b[ RTDXDX[ R[N[[ R\\G\\K R^C_F R[NbNb[ ReCeIkIkM RaGaK ReMe[lZl[ RTDTTR[',
	    'PoSJkJ R_C_[ R_J\\OTW R_JaOkW',
	    'PoRIlI RY[_[ R_C_[ RVSRW RfMhQkW RXMUU',
	    'PoUHjH RROkO R_C_[ R_NgVlY R_OWWRY',
	    'PoRHlH RTOjO R_C_[ R_OfVlY R_OXVRY',
	    'PoSJkJ RYVeV R_C_[ R_J]MUURV R_JaNhTlV',
	    'PoSJ_J Rb[k[ RYCY[ RbDbZ RXJ^P RYITT RlVkZ',
	    'PoRIkI RfYlY R_C_[ RfLfY RlUlY RWURY RcDiG RXMWV',
	    'PoSJkJ R_C_[ R`LjW R^JcO RVURW R_I]NUV RcDgF',
	    'PoTGjG RW[j[ R_C_N RZVVY RjWj[ R_FbJlN R_F[JRN RUPePXV',
	    'PoWIiI RROlO R_C_[ R_OfVlX RXDUK R_OWVRY',
	    'PoRI]I RXCX[ RWK]P RgVlZ R_V[Z RcDcMiY RXIRU RbO^Y RcJfU RXFVO RcGaS',
	    'PoRI]I RXCX[ RXM\\R RXIRV R_R\\Z RXGVP R`FgFhKjX RaI_V',
	    'PoRJ_J RYCY[ RcCc[ RYM^R RcMlQ RYJURRU',
	    'PoeMkM RRRlR R_N_[ RYDeDeL RkIkL RYJUN R`ReVlZ R^RYWRZ RYEXL',
	    'PoeOiO RRRlR R_O_[ RYJTO R`RdWlZ R[FXM R^RYWRZ RTEdEcJjIiO',
	    'PoSI]I Re[j[ RXCX[ RXM]Q R`V[[ RXISU RbQ^Y R]FlFlSj[ RcFcKaU',
	    'PoSI]I Rf[j[ RXCX[ RXM]Q RaV][ RXIRV RcQ`Y R^JlJlUk[ RdCdLbU',
	    'PoRI[I Rh[l[ RWCW[ R]JhJi[ RlVl[ RWIYN RWJRT RbCaR]Z RWGTR RbL`V',
	    'PoSI]I Rh[l[ RXCX[ R`EhEh[ RmWl[ RXM]Q RXISU R`E`N_T\\[',
	    'PoRI^I RYCY[ RbFbU RlDl[ RXJ^P RYISU RYGVQ RlS`U',
	    'PoRI^I Ra[k[ RXCX[ RaCaZ RXK]Q RlWk[ RXISV RgL`O',
	    'Po]ElE RSI\\I R`NkN Rc[h[ RXCX[ RXM\\Q RXIRV RaE_O RkOi[',
	    'Po^FlF RSJ^J Rb[f[ RXCX[ RgFg[ RXK^Q RXISU',
	    'PoSGkG ReSiS RUKUR R_C_[ RUKiKiR R_PeWlZ R_PYWRY',
	    'PoRQlQ R[[_[ R_M_[ RhUlY RWEiM RfEZKSM RYTTXRY',
	    'PoSI]I RXCX[ RhWl[ RXM]Q RaU[[ RfQiY RXISU RcP_X R_EeEeMhW ReIfT RdHbT',
	    'PoeNlN RSQkQ RZ[_[ R_N_[ RTGeGeM RlJlN RdTiY RYTSY R]D[JXLRN',
	    'PoRI\\I RXCX[ RbSjZ RXM\\R R_KdU RXIRV RjJfS`Y[[ R_HaR R]FkFhQ RXGVO',
	    'PoRI\\I R]NlN RWCW[ ReEe[ RWK[O RWJRU RWGTS RjD^F',
	    'Po^EkE RRI]I R]NlN R`[d[ RXCX[ ReEe[ RXM\\Q RXIRU',
	    'Po^EkE RSI\\I R\\OlO RXCX[ RdEd[ RXM\\Q RXIRV',
	    'Po_EkE RSI]JlK Rc[h[ RXCX[ RXM\\Q RXIRV RaK_T R`QjQi[',
	    'PoRI\\I RXCX[ RXM\\Q RaIeM R^KbTgYl[ RXIRU RjJfS`Y[[ R]FkFgQ RXGVO',
	    'PoRI^I RYCY[ RXJ]O RXISU RiDfG_J RjKgN`R RkShW^[ RXGVQ',
	    'PoRI\\I RXCX[ ReEeX RlDl[ RWKZN RXIRU RXFVO R_D_Q^X',
	    'PoRJ[J Rh[l[ RWCW[ R^EhEhZ RlVlZ RWLZP RbMdQ RWKRU RWHTT R^E^Q\\X',
	    'Po[FlF RRI\\I Rg[l[ RXCX[ RgFg[ RXM\\Q RlWl[ RXIRU R`F`R\\Z RWGVO R`M^W',
	    'PoSI\\I R]OlO R`[e[ RXCX[ ReKe[ RWK\\P R^FkEfJ RXIRU',
	    'PoRGlG RRUlU RZ[_[ R_C_M R_S_[ R`GcJlN R]GZKRN RWPfP_S',
	    'PoRHlH RWZgZ RWRW[ R_C_Q RWRgRg[ R`HfMlP R^HXNRP',
	    'PoSI]I R_IlI Rc[i[ RXCX[ RiCi[ RXK]P ReO^V RiHeP RXISU',
	    'PoRI]I R_IlI Rd[i[ RXCX[ RiCi[ RXM]Q RaNdT RXISU',
	    'PoSI\\I Rd[i[ RXCX[ RWK]P RbU\\[ RXIRU R`J^R RXFVO R^FlFlPj[ ReFeKcT',
	    'PoRI\\I R_QhQ Re[j[ RWCW[ RWM[Q RWJRU RaD]N RWHTT R`IlIlTj[',
	    'PoRI[I Rc[k[ RWCW[ RdFdZ RYLZO RlWk[ RWJRU RWGTS RjD]G RgN]P RkNbO',
	    'PoRI[I R\\KlK RWCW[ RgVk[ RWK[O RaU[[ RdDdQhY RWJRU RcQ`X RcLgW RWGTS RdLbU RdIeT',
	    'PoSI\\I R^IlI RXCX[ RXK[M\\P RcU^Z R_MbTgYl[ ReObW RWKRT RXGUQ RfCfJdS',
	    'PoSFlF RWMjM RRRlR RWFWM R_O_[ R^C`F R`RdVlZ R^RYWRZ',
	    'PoUJjJ RWOlO RaLa[ RUEjEjJ RbQjY RaN_RXY RUEUQSY',
	    'PoSI]I RXCX[ RXM]R RgDjH RgZj[ RXISU RdOfY RlVk[ RcCcLeW RdL^L RlKfK`L RjKcL',
	    'Po`HlH RRI[I R_[k[ RWCW[ RYLZP R_MiM^X RkWk[ RWJRU RbC]M RWGTR',
	    'PoRI]I R`KkK Rb[i[ RXCX[ R^EkEkK RWK\\P RXIRV R_QlQjZ R_K_Q',
	    'PoRI\\I R^LkL R\\ZlZ RXCX[ RdDdZ RXIZN RXIRU',
	    'PoSI\\I RgTjT R_[k[ RXCX[ R_F_Z ReDeV RkIkT RXM\\Q RlWk[ RWJRU RWHTS RiJcL]N',
	    'PoRI[I R^OjO R^[k[ RWCW[ R^O^Z R]EjEjP RWIYM[P RXIRU RlVk[ RWFVO',
	    'PoSGkG RUQiQ RUKUQ R_C_[ RUKiKiQ R_QdVlZ R_QZVRZ',
	    'Po]FkF RRI\\I R\\YlY RXCX[ RdFdY RWJ\\O RXIRU RXFVO',
	    'PoRSlS R_O_[ R`SdWlZ R^SZWRZ R\\CWHSJ RYG]KbMkP RYFeFaK[NSP',
	    'PoRHlH RVSiS RS[l[ R_C_Q R_S_[ R_GeNlQ R_GXNRQ',
	    'PoSI\\I R^YlY RXCX[ R^K^[ ReCeY RlKlZ RXM[Q RXIRU RWFVP',
	    'Po\\FlF RSI\\I Rd[i[ RXCX[ RXM\\Q RXIRU RbFaNkMkUi[ RaJ_W',
	    'PoTGjG RRPlP R_C_[ RWIYN R_OgWlY RgIeN R_OWWRY',
	    'PoRI\\I RWCW[ RWM[Q RdT][ RfPjZ RgKcV RVMRU RbE]O RWHTS R`IhIeR',
	    'PoRI\\I R^IlI RaYlY RXCX[ RaIaY RWK[N RcDfG RXIRU RXFVO',
	    'PoRI[I Rf[j[ RWCW[ RbO\\U R`Y\\[ ReS^Z R\\EhEdH RWIYMZP RhLdU RVLRT RbL`Q RWGTS RdI]LlLlRjZ',
	    'Po]EiE RRI[I R\\UhU Re[i[ RWCW[ RWKZN RWHST R_H^PlPj[ RiFhP',
	    'PoSI\\I RaRdR RXCX[ ReCeR RXM\\Q RiGlN RWJRU R`G]N RjPhUdX][ RWHTS',
	    'PoRI\\I ReMlM R[ZlZ RWCW[ R_I_Z ReCeZ RWK[O RWJRU RWGTS',
	    'Po^EjE RRI[I R\\MlM Rf[l[ RWCW[ RgMg[ RlWl[ RWIYO R_W[[ RWJRT RWGTR RaN`W',
	    'Po\\HlH RSI\\I Rh[l[ RXCX[ RdCdH R`MhMh[ RXM\\Q RlWl[ RWJRU R\\Z[[ RWHTS R`M`S_X',
	    'Po\\HlH RRI[I RgXkX RWCW[ R]M]X RdCd[ R]MkMkW RVIYP RWHSU',
	    'Po\\FlF RSI\\I RXCX[ RdId[ RWK[O RfLlR RdJ\\R RXIRU RfFcL',
	    'PoRHkH R_C_S RhVkZ R[V]Z RbVcZ R_GaJhPlR RUVTZ R_G]JVPRR',
	    'PoRFlF RUNiN RUSiS RUJUS R_C_[ RUJiJiS R_RcVlZ R_R[VRZ',
	    'PoVIhI RVMhM RRRlR RVEVM R_M_[ RVEhEhM R_QdVlZ R^RYWRZ',
	    'PoRGlG RWUhU RWZhZ RWQW[ R_C_O RWQhQh[ R`GeLlO R^GXMRO',
	    'PoRI\\I RWCW[ RfVk[ RWM[Q RbU\\[ RdHcQhX R_IlIiN RUPRV RdP`X RdMfW RWHTT R`C]M RdKdPbU',
	    'Po_IkI RRI[I R\\QlQ RWCW[ RdHd[ RWJ[N RWJRU R`D\\L RWGTS',
	    'PoRI\\I R^TjT Rd[k[ RXCX[ R^K^V RdCd[ RjKjU RXM[Q RlXk[ RWJRU RWHTS RjF^G',
	    'PoRI[I R^PkP R^[k[ RWCW[ R^E^Z RdEdP R^EkEkR RWJ\\O RlWk[ RWJRU RWHTS',
	    'PoRI[I RgLlL R\\PiP RWCW[ R_DgDgL RWIYMZP R]P_ScWk[ R]L\\M RiQcW[[ RWGTS R_D_L',
	    'PoRJ[J ReMkM Re[k[ RWCW[ R`H`[ ReCe[ RlWk[ RWMZP R_I[M RWKRU RaC]L RWHTT',
	    'PoRI[I RbZhZ RWCW[ RcJcZ RhUhZ RYLZP RWIYN RaEfG RWJRU RiLkU RWGTR R^M\\V',
	    'Po]EiE RRI\\I R\\OlO R[ZlZ RXCX[ R`S_Z R`Q`W RaMaU RaJaR RaEaO RiNiZ RiGiV RXM\\Q RXIRU RXGVP',
	    'PoRI[I R`[d[ RWCW[ RdMd[ RcIfL RWIYO R]MlMiS RWJRU R^EjEgHcI RWHTS',
	    'PoRI[I R\\LkL R[RlR R_[d[ RWCW[ RdFd[ RWKZN RVMRU RWHTS RjD]F',
	    'PoSI\\I R]ZkY RXCX[ RXM\\Q RiUl[ RgDlN RWJRU RaD]N RcN_Z',
	    'Po^EkE RSI\\I RXCX[ RXM\\Q R`LdUiYl[ RWJRU R^LkLgTcY_[ R^E^Q]Y',
	    'PoVNeN RRSlS R_P_[ RcJhO R`SdWlZ R]EdEfGlI R]GZN R^SZWRZ RYEWHRI',
	    'PoSI\\I RXCX[ RXM\\Q R_E_OcThYl[ RXIRU R_MkMhTcY^[ R]EhEgM R_K]X',
	    'PoRI]I R`KlK R_RlR Rg[l[ RXCX[ R`ElEl[ RWK\\O RXIRU RXFVO R`E`R^X',
	    'PoRI\\I R_NhN R^ShS Re[j[ RXCX[ RWK\\P RXIRU RbC]M RXGVP R`IlHlTj[',
	    'PoSI]I Re[j[ RXCX[ RXM\\Q RXIRU RaD\\M RbL_U R_HlHlSj[ RcU]U ReQgUaU',
	    'Po\\EkE RRI[I R[NlN RWCW[ RhEh[ RWM[Q RWJRU RWHTS R`E`Q_X',
	    'PoRI[I Rc[k[ RWCW[ RcFc[ RWIYMZP RlXk[ RWJRU RWGTS RiD\\G RjJ\\M RlQ[S',
	    'PoRJ[J R]LcL Re[k[ RWCW[ R]D]Z RfDfZ RWMZP RlVl[ RkJiLeN RWHTT RcY[Z',
	    'PoUH]H RaOkO RSRkR RUCUO R_O_[ RaCaN RkKkN R`RcVlZ R_R[URY RiFaI R^MTN',
	    'Po]FkF RRI[I R^OkO R\\ZlZ RWCW[ RdFdZ RYM[P RWJRU RWGTS',
	    'PoSIZI RgVlV RWCW[ R]F]V RdFd[ RdFlFlV RVJXNZQ RWHSU RcD\\E RbT[V',
	    'Po]HlH RSI\\I Rd[i[ RXCX[ ReCeH RXM\\Q RXIRU RbHbPkOjUi[ RbK`W',
	    'PoSI[I Rc[h[ RWCW[ RWN[Q R`W\\[ R`EgEkM RWKRU R`G]M RWHTT RbN`W R^NiNiUh[',
	    'PoRI[I R^KiK Rb[g[ RWCW[ RfFlK RaE_H\\K RYM[P RWJRT RWHUQ RbK`QiQh[',
	    'Po^IkI RRI\\I R]PlP RXCX[ RfVl[ RXM[P RbU\\[ RdCdPhX RXIRU RdP`X RdKbU',
	    'PoSI\\I R]LlL R]SlS Re[i[ RXCX[ R`E`[ R`EiEi[ RWK\\P RXIRV',
	    'PoSI\\I R_MlM RXCX[ RgMg[ RXM\\Q RXIRU RjD_F_P^X',
	    'Po\\EkE RRI\\I R[[l[ RXCX[ RiNhT R_KiKiR RXM\\Q RXIRU RXGVP R^TiShZ R`E^U',
	    'Po`ElE RSI\\I R\\OlO Rd[i[ RXCX[ R`E`N RiEi[ RXM\\Q RXIRV RiN`W\\X',
	    'PoRI\\I RXCX[ RgCg[ RWK[O R^McO R_FdH RXJRU RXGTR RgT]U RiS`U RlScT',
	    'PoRI[I RWCW[ RbJdL RdDlL RdD\\L RWIYMZP R]QiQd[ RWJRT RWGTS',
	    'PoSI\\I Re[l[ RXCX[ R^I^M ReCeZ R^IlIlM RXM\\Q RWJRU RlVl[ ReHcQaW][',
	    'PoSI\\I R]NlN RXCX[ RgVl[ RbU\\[ RXM[P[Q RdFdQhY RXIRU RcP`X RdLgW RdNaV RdKcT RjD]F',
	    'PoSI]I R`IkI RYCY[ ReCe[ RXJ^O ReIkV RXISU ReI`U ReGhR RXGVQ ReGcQ',
	    'PoSI\\I Rg[l[ RXCX[ R^J^[ R^JlJl[ RXM\\Q RfQjU RcQ_U RXIRU R_DeDeMbS RXFVP ReHfQ',
	    'Po^ElE RSI[I RfSjS Rb[k[ RWCW[ RbJbZ RaJjJjS RWMZP RlWl[ RWHTT R^E]Q\\Y',
	    'PoSI\\I RXCX[ RXN\\R RaD]N RXIRV R_IcThYl[ R_IjIeTaX\\[',
	    'PoRI[I RWCW[ RcFc[ RWM[P RVMRU RWHTT RiEiLiOkZ R]F]Q\\Y RjE]F',
	    'PoUIiI RUNiN RRRkR RUEUN R_E_[ RUEiEiN R`RdVlZ R_QZVRZ',
	    'Po]HlH RSI\\I RXCX[ RdCdM RXM\\Q R_OaTfXl[ RXIRU R]NjNhScX\\[',
	    'PoRJ[J RWCW[ RYMZQ R_D_NbT RhRl[ ReRaZ R^SZ[ RWHSU RfDfLiW RfKdW R_K]W',
	    'Po^FkF RRI[I R\\NlN RWCW[ RYLZP RWIYN RgSk[ RWJRU RcN^X RWGTR RdX]Y RjXbY',
	    'PoSI\\I RhNlN R\\SlS RXCX[ RdNd[ R]GhGiN RlKlN RXM[P RXIRU RcCaJ]N',
	    'PoRI[I R\\NlN RWCW[ RhDh[ RWM[Q RWJRU RWHTS ReE\\G RaFaQ_X',
	    'Po^ElE RSI[I R^ZlZ RWCW[ R^E^[ RWN[Q RcMjT RdQ`V RhHdS RWJRU RWHTS',
	    'PoSFkF RVJVO R_C_Q RVJiJiO R_KcOkS R_K[OSR RZSfU RWXgZ',
	    'PoSI]I R\\LkL RXCX[ RfVk[ RXM]Q RgEkI RaU[[ RcDdQhY RXIRU RcQ_X RdLaV RcJfU',
	    'Po]ElE RSIZI Re[i[ RWCW[ RYLZP RWIYN RWGSS R`LkLkRi[ R]E]Q\\Y RdGdObX',
	    'Po]ElE RSI\\I R_QkQ RXCX[ R_JkJkR RXM\\Q RXIRU RXGVP R_J_R]Y',
	    'PoSI[I ReZkZ RWCW[ R^E^R ReSeZ R^EiEiR RbU]Z RWIYMZO RWJRU RlVkZ RWGTR RcHcPbU',
	    'PoSIZI R\\NlN RWCW[ R`D`[ RgUlZ RjEcK RVIXNZQ RdNhW RWHSU ReY^[',
	    'PoSGkG R\\ZhZ RYZeZ RVZaZ R_C_N RdVi[ RYPTU RaGdJlN R\\PdPgSlT R\\TYZ R^GZKRN',
	    'PoRJ[J RbTgT R_[j[ RWCW[ R_M_Z RjVjZ RWK[O RdEkM RcCbG[M RWJRU RWGUP R_MhMgT',
	    'PoSI[I RWCW[ RlXl[ RWKZN RaKgX RfJ`W RWGST R]EiEiUjZ R]E]R[Y',
	    'PoRI[I R\\OlO RWCW[ R_DiDi[ RWIYMZP RbHfL RWJRU RWGTS Rf[i[f[ R_D_R^Y',
	    'PocLgL RSShS RfUjU R_P_[ R^C\\F R[I`J R_RbVkZ R^R[URY RVFVNlNjU RVFhFgL',
	    'PoRI[I RdJlJ R^ZjZ RWCW[ R^P^[ RdCdP R^PjPj[ RWKZN RWJRT RWGTR',
	    'PoRI[I R\\JlJ R]ZjZ RWCW[ R]Q][ RdCdQ R]QjQj[ RWKZN RWJRT RWGTS',
	    'Po]EkE RRI\\I R\\QlQ RXCX[ RdEd[ RXN[R RiIkN RXJRU R`H]N RXHTS',
	    'PoSI\\I R]MhM R^YjY RXCX[ R^Q^[ R^QjQj[ RXM\\Q RjKkO RXIRU RbC^M RgHjLeM RXGVP',
	    'PoSIiI RVPhP RRTlT RVKVP R_P_[ RVKhKhP R[CWH R`TbWkZ RbEjI R]T[VRZ',
	    'PoSI\\I R_QkQ RXCX[ R_E_Q R_EkEkQ RXM\\Q RhTl[ RXIRU RaT^Z RXGVP',
	    'PoSI\\I R_LkL Rf[k[ RXCX[ R_E_L R_EkEkL RXM\\Q RbV^[ RXIRU RcPaX RXGVP R^PlPlVk[',
	    'PoSI[I R^KjK R[OlO Rb[h[ RWCW[ R^E^K R^EjEjK RWM[Q RWJRU R`O]U RWHTS R^TjSh[',
	    'PobNkN RSRkR R_O_[ RbFbM RbFkFkM RVKRO R_RdWlZ R^RYWRZ R\\NYN RXCVL RRF^F\\N',
	    'PoRIZI RfYlY R_[b[ RWCW[ RfGf[ RfGlGl[ RXNZR RWKYP RWHST R_C_O]Y R\\IdIcVb[',
	    'PoSI[I R_TfT Re[j[ RXCX[ R_M_V R_MfMfT RWJ[N RXIRU RaD]M RWFVP R_HlHlTk[',
	    'PoRI[I R^SfS R_[k[ RWCW[ R_L_Z R^LfLfR RWM[Q RlWk[ RWJRU R`C\\M RWHTS RjUfU R_HkHkPjU',
	    'Po]HkH RSI[I R^OjO RWCW[ RdCd[ RgTlY RaT\\Y RWIZP RdMgU RdM`U RWGSS',
	    'PoRI[JlK RcTiT R^ZlZ RWCW[ R^D^[ RcDcT RiDiT RWM[P RWHST',
	    'PoSGkG R^KfK RWOkO RSSkS RWDWO R^C^K R_P_[ RfCfK R_SbVkZ R_S[VSZ',
	    'PoTFkF RSKlK RWOgO RSTkT RWHWO R_C_K R_O_[ RgHgO R_SbVkZ R_S[VSZ',
	    'Po\\HlH RSI[I R^QkQ RWCW[ RdCd[ RkKkX RWN[Q RWJRU RWHTS R^L^Q]W',
	    'Po]HkH RSI[I R\\QlQ RXCX[ RdCdL RhLh[ RWJRU R\\Z[Z RWHTS R`L`R_W',
	    'PoRI[I RWCW[ RdCdO R]HkHjL RWM[P R_OaTfXl[ RUPRV R]OjOhScX][ RWHTT R]H]Q[Y',
	    'PoSI\\I Ra[k[ RXCX[ R^H^K RaLa[ RdCdG R^HlHlK RlWk[ RXM\\Q RWJRU RiN`S RWHTS',
	    'Po^HlH RRI[I R_[k[ RWCW[ R_L_[ ReJeW RWLZO RlXl[ RUPSU R`C\\L RWHTT RjM\\Q RjMjUgU',
	    'PoSI[I R_LhL RgYjY RXCX[ RbQb[ R]QjQjX RXM\\Q RdDlL RbE[L RWJRU RWHTS',
	    'Po]ElE RSI[I Rg[l[ RWCW[ R]J][ R]JlJl[ RdEdOjT RcQ_U RYLZP RWGSS RdJcQ',
	    'PoSJ[J R^KkK Rb[k[ RWCW[ RbMb[ R^EkEkK RWLZO RlWk[ RWJRU RjPaT RWGUP R^E]R\\Y',
	    'Po]IlI RSI\\I R\\ZlZ RXCX[ RdDdI RXM\\Q RXJRU RXHUR RiKfZ R`LbW',
	    'PoRI\\I R]LkL RXCX[ ReEe[ RWK\\O RhSlW R`T]X RdJiU RXIRU RdK_V RXFVO RjD^F',
	    'PoRI\\I R^LjL R]SlS RXCX[ RdCd[ RXL\\P RjFkJ RhEkG RXIRU R`E^J RXGVP',
	    'PoSJ[J R]MkM Rb[l[ RWCW[ ReFeL RjNaW RWLYN[Q RWKRU R]V[[ RWHTS R`Zh[ RjD_F',
	    'PoSI\\I RWCW[ RWL[P RaG\\K R`GaJgPlR RbRgU RWJRU RaC_I R`GiGgJbO[R R^Xi[ RWHTS',
	    'PocHkH R`HiH R^HfH R[HcH RRRlR RWFWO R_O_[ RkLkO RYDRK R`EbKeMiO R`ReWlZ RdDhF R^RYWRZ R^HYH',
	    'PoRI\\I R^IhI R`UgU Rg[l[ RXCX[ R`M`W R`MgMgT R]ElEl[ RWK[N RXIRU RXFVO',
	    'PoSI[I R[QlQ RWCW[ R^H^Q R^HjHjQ ReVj[ RWIYN RdCdQgX RWJRT RbU^YZ[ RWGTS RcMbU',
	    'PoRI[I RaJlJ Re[i[ RWCW[ R`H`[ RiCi[ RWMZP RcOeT RWKRU RaD\\O RWHTT R`E_L',
	    'PoRI[I R_QlQ R_ZlZ RWCW[ R_H_[ R_HlHl[ RWK[O ReCcH RWHTT',
	    'PoRFlF RXJfJ RXOfO RRSlS RXCXO R_O_[ RfCfO R`SdWlZ R]SYWRZ',
	    'Po\\IlI RRIZI R_QiQ R_YiY RWCW[ R_C_[ RiCi[ RWIYN RWJRT RWGTS',
	    'PoaNkN RSSkS R_P_[ RaCaN RkKkN RXJTO RUDYD RRGWH R`ScVlZ R]SZVRZ RcGZI RfG]H RhG`H RkGcG',
	    'PofOkO RRRlR R_O_[ R[GgGgN RkKkO RWKSO R_K[O RSHWI R`ReWlZ R]RYWRZ RTDYE R`C_K',
	    'PoRSlS RSJkJgN R`ScWlZ RVEgE`H R^KSP R]SZWRZ R[GeJ R_J_P\\P R_R_[',
	    'PoSI\\I Ra[e[ RWCW[ ReMe[ RWM\\Q R^EjEeJ RcP[X RaHfK R\\NlMjR RWJRU RdMbR RWHTT',
	    'PoSI\\I RfNkN R_ZkZ RXCX[ R_Q_[ R_QkQk[ RcJ^N RYLZO RWJRU RdFaL RWGTR R^ElEkN',
	    'PoRI\\I R^YjY RXCX[ R^P^[ R^PjPj[ RXM\\Q RaEgEkN RXIRU R`G]N RXGVP',
	    'Po\\ElE RSI[I R_ZjZ RWCW[ R`M`[ R_OjOk[ RYKZO R_OZT RWJRT RcE]Q RWGTR',
	    'PoSI[I R^KlK R^RlR RWCW[ R^E^S ReEe[ R^ElElS RWIZO RWJRU RWGTS',
	    'PoRI[I R^QlQ R^YlY RWCW[ R^I^[ ReCeY R^IlIl[ RYLZO RWJRT RWGTR',
	    'PoSI\\I R^NlN R^TlT RXCX[ R^H^U ReCe[ R^HlHlU RXM\\Q RWJRU RWHTS',
	    'Po]ElE RSI\\I R_SjS R\\[l[ RXCX[ R_E_Z R_KjKjS RXM\\Q RXIRU',
	    'PoSI\\I R_LlL RXCX[ RfLf[ RXM\\Q RaPlU RXIRU RjD^F_Q]X',
	    'Po_HlH RSI\\I RcMlM RcTlT RXCX[ RcHc[ RXKZM[P RWKRT RaC]L RXGUR',
	    'PoRJ[J R[NlN R[SlS Rf[j[ RWCW[ R]H][ RcCcS R]HjHj[ RYNZQ RWKRU RWHTT',
	    'PoRI[I R]NlN Ra[d[ RWCW[ R]H]L ReNe[ R]HlHlL RXKZO RdDdF RWJRU RWHTS',
	    'Po]FjF RSI[JlK R`[c[ RWCW[ RdKd[ RfTlY R_T[X RkNfS RWIZQ RdMhV RWKRU R\\PaP^V RWHTS',
	    'PoRI[I R^MlM R[ZfZ RWCW[ R^F^V RWM[Q RWJRU RePhY RWHTS RiD]F ReEeLgW RdU[V RlVkZiZ',
	    'Po_IkI RSI[I R\\PlP RWCW[ RfVk[ RWM[Q RbU]Z RdCdQhY RWJRU RWHTS R`E]M RdLcU',
	    'PoRI\\I R_LjL R_SjS R[ZlZ RXCX[ R_E_Z R_EjEjZ RXM\\Q RXIRV',
	    'PoRGlG RXTfT RXXfX RS[k[ RXPXX R_C_N RXPgPgX R`GdLlO R^GYLRO',
	    'PoRI\\I R^MjM R^UjU R\\ZlZ RXCX[ R^E^U R^EjEjU RWJ\\O RWJRT RWGTR',
	    'PoRI[I RWCW[ RWM[P RWJRU RWHTS RiDiNkZ R]E]Q[Y RcE]F RjD`F RcGcY^Y RfThY`Y',
	    'Po]GlG RRIZI RhXjX RWCW[ R`L`X ReJe[ R^NkNkX RXLYP RVIXN RbE]Q RWGSR',
	    'Po]ElE RRJ[J R]ZlZ RWCW[ R]E][ RhSlV ReQ`W RYNZQ RgOjU RbJhKdT RWKRU RcG_P RWHTT',
	    'PoRIYI RVCV[ R]SZW RVJYQ ReSb[ R_D[N RaK]S RaScY RVHRU RVFTS RbVZW RjZgZ RcIlIlTk[ RgCfMdW RaMZN',
	    'PoRIZI R]MkM RhXkX RWCW[ RfCf[ R]GkGkM R`W\\[ RXMZQ RWGST R\\M\\RlRkX RaCaQ`W',
	    'PoSGkG RURiR RUJUR R_C_[ RUJiJiR RYL[P R_QdWlZ ReLcP R_QZVRZ',
	    'PoRI[I R]JlJ RWCW[ RWIYM[Q RfDiG RaCaOdUhYl[ RWJRT RaNiNfUbY^[ RWGTR RaJ_W',
	    'PoRI\\I R^NlN R^YlY RXCX[ R^R^[ R^F^N ReCeY RlFlN RlRl[ RXM\\Q RXIRU RXFVP',
	    'Po]ElE RRI\\I R^TeT Rd[i[ RWCW[ R^J^U R^JeJeT RiEi[ RWKZN RWJRU RWGTS',
	    'PoRGlG RYOfO RTSjS R[[_[ R_C_M R_S_[ RgWj[ RdVhX RYVUZ R`GdKlN R]GZKRN',
	    'Po^IlI RSI\\I R_QkQ R\\ZlZ RXCX[ ReIeZ RXM\\Q RbCfG RXIRV',
	    'PoSI\\I Ra[i[ RWCW[ RaIa[ RiWi[ RWL[P ReQ\\Z RaDeE RjGdR RVLRU RjMlU RWHTS R^N\\U',
	    'PoRIZI RhVlV RWCW[ R\\F\\U RfFf[ RfFlFlU RWKYP RcC\\F RWIST RbHbSaW\\[ R]TaS',
	    'PoYG^G RaNkN RSRkR RTFTN RYCYM R_O_[ RbCbM RkKkN R`RdVlZ R]RYWRZ RjFaH R^LRN',
	    'PoRJ[J R[OlO Rh[j[ RWCW[ R]E][ RbEb[ RfEf[ R]EjEj[ RWMZP RWHTT',
	    'PoSI[I RfQlQ R]YlY RWCW[ R]E][ RgEgQ R]ElEl[ RWLZO RVMRU RWHTT RbEbLaQ',
	    'PoSI\\I R^OjO Rf[l[ RXCX[ R^E^O RgOg[ R^EjEjO RlWl[ RXM\\Q R_W[[ RXIRU RXGVO RaO_X',
	    'Po\\FlF RRI[I R[ZlZ RXCX[ RdHdX RWK[O RfKkQ RcJ[R RXIRU RdFbL RXGVP',
	    'PoRI\\I RXCX[ RdCd[ RXM[Q RePbR\\X RXIRV RkXgX RXFVP R^I]QlPkX RkG^H',
	    'PoRI\\I R_MkM R]QlQ Rf[k[ RWCW[ R_E_M R_EkEkM RWM[Q RbV][ RVLRU RWHTT RdMbW RlRk[',
	    'PoRGlG RXTgT RXWgW RR[l[ RXPX[ R_C_N RXPgPg[ R`GdKlO R^GYLRO',
	    'PoXCX[ RcCcH RRI\\IkHkK RXM\\Q RhTl[ RWJRU R`Q\\Z R\\H\\K RWHTS R`MgMiX',
	    'PoSI[I R\\TjT R[ZlZ RWCW[ RdPdZ RWL[P R_GdMkP RWJRU R\\EiEhHcM]P RWHTR R_E_G',
	    'Po\\ElE RSI\\I RdOkO R[ZlZ RXCX[ R^L^Z RdEdZ RXM\\Q RWJRU RWHTS',
	    'Po\\GlG RSI[I RgXkX RWCW[ R^M^X RdDdG RdId[ R^MkMkW RWM[Q RWKRU RWHTS',
	    'PoRI[I R]JlJ ReUiU Ra[k[ RWCW[ RaNaZ RWK[O RkWkZ RVMRU RWHTS R`NjNiU R]F]R[Z RdE]F RiD_E',
	    'PoSI[I RhVlV RWCW[ RfFf[ RfFlFlU RWIYN R_W[[ RXIRU R\\MbQ]Y R_D[P RcK_W RWFUQ R^HcHaU',
	    'PoRI[I R[NlN R]XlX Rd[h[ RWCW[ RaPeT RWM[P RbGfK RWJRU RWHTT R_EjEjUi[ R_E^I^L^O]X',
	    'PoRI[I R^KlK Re[i[ RWCW[ RWM[Q RWJRU RWHTS RdPkPkUi[ R^F^Q\\X RdLbW RjD^F',
	    'PoSPkP RTITM R_K_[ RTIkIkM RVDXH RhEdI R]D_H R_PeVlY R_PXVRY',
	    'PoRIYI R[NlN R_[a[ RVCV[ R]DaDa[ RVIRV ReDjDj[g[ RVFUQ R]D]R[Y ReDeTd[',
	    'PoSFkF RUNiN RUJUN RUJiJiN R_C^NaOfRlS R[TeU R^OXRRT RWXgZ',
	    'Po_FkF RSJ\\J R^LlL R`[d[ RXCX[ RdLd[ RWK\\O RhQlX RXIST R`Q]X',
	    'PoSI[I RWCW[ RYLZO ReEhG RWIYN RdRgZj[ RWIRT RkQfV\\[ RcMfX RlWk[ RWFUP RbCbIdU RiI]J RkN]P',
	    'Po\\GlG RRJ[J RfWjW RWCW[ R`C`J RbMb[ RgCgJ R\\MjMjW RVKYN RWKRT RWHTS',
	    'PoSI[I R]JlJ Re[l[ RWCW[ ReJe[ RlXl[ RYLZP ReEiF RiQaY RjNhR RWJRU R`R\\[ RWGTS RaCaL_V',
	    'Po\\GlG RSJ[J R]PjP R`[d[ RWCW[ RdKd[ RWJYO RiTkZ RWKRU R_T]X RWHTS RbC^N',
	    'PodFlF RRI]I R_QkQ RXCX[ RdCdK R_KlKlS RWJ[N RXJRT RXGUR R_K^S]Z',
	    'PoRI[I R_JlJ R^RkR R\\ZlZ RWCW[ RdCdZ RWIYMZP RWJRU RWGTS R`E]N',
	    'Po^EfE RSI[I R]NlN Ra[d[ RWCW[ RdHd[ RWIYO RiRlY RUORU R_R\\Y RWGSS R^E]N RjDaE',
	    'PoSI[I R^JkJ R_QiQ R\\ZlZ RWCW[ RWIYM[P R`DbH RWJRU RhDfJ RWGTR',
	    'Po^EiE RRI[I R`[d[ RWCW[ R]IdId[ RWM[P RhTlX R_TZX RkLfQ RdKiV RWKRU R[NaN]V RdIfR RWHTT',
	    'Po\\GaG RRIYI RcJlJ Re[i[ RVCV[ RiCi[ RVIYQ R]MaU ReNgS R`P[Z RVIRV RaK]W RaH^U RVFUQ',
	    'PoRI\\I R_QgQ R_UgU Re[j[ RXCX[ R_M_W R_MgMgU RXM\\Q RWJRU RaC]M RWHTS R_IlHlTk[',
	    'PoRI[I R^MiM R]SjS R\\ZlZ RWCW[ RdMdZ RWM[Q RdDlK RcE\\L RWKRU RWHTS',
	    'PoeOiO RSSkS RYDYKYQ R_P_[ RcK_O R_RbVkZ R_S[VRZ RdEbM R]HTK R\\ERG R]MRO R_EjEjJiO',
	    'Po^ElE RRI[I R^SkS R^ZlZ RWCW[ R^E^Z ReEeK ReSeZ R^KkKkS RYLZP RWIYN RWJRU RWGTS',
	    'Po\\ElE RRIZI RfRlR R]YlY RWCW[ R]J][ RbEbP RfEfQ R]JlJl[ RXLYO RWGTS',
	    'PoSEkE RUOiO RRSlS RUIUO R[E[O R_O_[ RbEbO RUIiIiO R`SdWlZ R^SZWRZ',
	    'PoSI[I RWCW[ RWM[P RaG\\I RWJRU RdC_HcJeL\\P RbR\\T RaFjGeM`SdTgW_Z RWHTS RcQlQjTeX\\[',
	    'PoRI[I R]QkQ R]WlW RWCW[ R^S]X ReNe[ RaE\\J RYNZQ R`GdKkN RWKRU RaFiFdK]O RWHTT',
	    'PoRI[I R]IkI R\\PlP RWCW[ RfUk[ R_DbG RbU\\[ RWJZP RiDdIdQgX RWJRT RdP`W RWGTS RdMbU',
	    'PoRIZI ReLiL R^[a[ RVCV[ RbH^L RVJYP RVHRU RcCaJ R\\FjFiL Rk[h[ R^N\\Y ReQlQk[ RVFTQ RZQbQa[ RhNfY',
	    'Po\\GlG RRJ[J RhRkR RWCW[ R]K]R RdCd[ R]KkKkR RWM[P RdRkY RdQ]Y RWJRU RWHTS',
	    'PoSI[I R]LlL R^ZjZ RWCW[ R^R^[ ReEeR R^RjRj[ RWJ[N RWJRT RWGTS RjD]F',
	    'PoSE]E R`EkE RRJ]J R`JlJ RRRlR RXEXO R_O_[ RfEfO R`RdWlZ R^RYWRZ',
	    'Po]IkI RRI[I R\\QlQ RWCW[ RhIh[ RWN[R R_W[[ R_CaH RUPRV RiCgH RWHST RaI`R_W',
	    'Po[EjE RZJlJ R[OkO RRSlS RWGWP R_Q_[ RcEcO RYCWGRK RcUlY R\\VSZ',
	    'Po\\HlH RRI[I RWCW[ RdCdG RWM[Q RgJlO R`K\\O R`ObThYl[ RWJRU RhOeTaY\\[ RWHTS',
	    'Po\\ElE RRI\\I R]RkR R]YkY RXCX[ R]K][ R]KkKk[ RYLZO RXJRU RXGTR RcEbK',
	    'PoSI[I R`OlO RaZlZ RWCW[ R_H_[ RgFgZ R^I[M RVIZP R`D]K RWHST RkEaG',
	    'PoRJZJ Ra[d[ RVCV[ ReCe[ RZE]H RVJYQ RkIfMkW R]PYX RbN^W ReGhT RVIRV R^KcK`T',
	    'PoRSlS R_Q_[ RbKkO RTEYG R`ScWlZ R]GkGiJ R^C[J RcI_N[O R]SZWRZ RZLRN',
	    'PoRI[I RWCW[ RfDf[ RlDl[ RVIYP RhKiQ R\\K^Q RbKcQ RWGST R_D_R^X',
	    'PoSI[I R]LkL R]QkQ RWCW[ R]G]S R]GkGkQ RfXi[ RYMZQ RWJRU RjSfW^[ RlWk[ RWHTS RdCdNfY',
	    'Po[I_I RRJZJ RWCW[ RgVl[ RcV^[ RVKYQ RfHeQiY RePbX RaIkHiO R_RZT RWISU RbC_N ReMdU',
	    'PoRJ[J R^[b[ Rg[l[ RWCW[ R\\EbEc[ ReElEl[ RWMZP RbO`Q[V RkHeM RjPeV RaH\\M RWHTT',
	    'PoSI[I R_IjI R\\PkP RWCW[ RdCd[ RgTlY RaT[Y RWIYM[P RePhU RWJRT RcP`U RdNbS RWGTS R_E]M',
	    'Po\\HlH RRI[I RaRlR Rc[g[ RWCW[ R_L_[ RgPg[ RWKZN RbLkLgP R^MZQ RWKRU RbE]P RWHTT',
	    'PoRI\\I R^RkR R^[k[ RXCX[ R_K_Z ReKeR R^KkKkS RlWk[ RXM\\Q R_H[L RXJRU RaC^J R`GgGdM RXHTS',
	    'Po\\ElE RRI[I RWCW[ R]K][ RbJbZ RfKfZ RWMZP RWKRU RWHTS R]KkKk[i[ RcEaN',
	    'Po\\ElE RRI\\I R_KiK R_QiQ RXCX[ R_E_W RiEi[ RXM\\Q RXJRU RXHUR RdV]W RhVaW RkUdV',
	    'Po\\GlG RRI\\I R`PjP R`UjU Rf[j[ RXCX[ R`J`[ R_MjLj[ RWK[N R`L[P RXIRU RbD^O RWGVO',
	    'PoRI[I ReKlK RbLhL RWCW[ R_I_[ RWN[Q RhDkG RgTaZ RjMfV RXIRU R`D\\N RlWk[i[ RWGVP ReDeNgX',
	    'PoRI[I R]JkJ R\\RlR RXCX[ R`C`R RhChR RXL[P RgUlZ R`V[[ RWJRU RWHTS',
	    'Po]GhG RRI[I R\\LlL R`PkP Rd[h[ RWCW[ RcCcL RWK[O RWKRU RkDjGaPZS RWHTS RaO`UjTi[',
	    'Po]GhG RRI[I R\\MlM Ra[k[ RWCW[ RaQaZ RcCcM RlWk[ RWIZP RkEiI`RZU RiRaU RWGST',
	    'Po_HkH RSI[I R\\SlS Rf[j[ RWCW[ R_L_[ RdLdS R_LjLj[ RWMZP RWKRU R`C]K RWHTT',
	    'Po\\EfE RRIZI R_KdK Rh[l[ RWCW[ RgGgU RlCl[ R_PbS RaV\\[ RVJYQ RcP_Y RVHSU R`E]Q RdLaV',
	    'PoRI[I R_LjL R]ZlZ RWCW[ R]D][ R]DlDl[ RYLZP RWIYN ReGeOgQiW RWJRU RWGTR RdJcS',
	    'Po]IkI RSI[I R^NjN R[TlT RWCW[ RdId[ R_C`G RWIZP RUOSU RjCgI RWHTS',
	    'Po\\HlH RRI[I RWCW[ RfUl[ RWM[Q RhKaR RWJRU RcI_MbR\\T RkPeW\\[ RdDdH',
	    'PoSI\\I R_JjJ R_OjO RXCX[ R_E_Z R_EjEjO ReUj[ RXM\\Q RcOfW RWJRU RkQiTeU RWHTS ReY\\Z',
	    'Po^GlG RSI\\I R^MlM R^UkU R^ZkZ RXCX[ R_C_L R^P^[ R^PkPk[ RXM[Q RWJRU RWHTS',
	    'PoRI[I R\\IlI R\\OeO RWCW[ RaOaX RiDkG RWI[Q RUORU RWHTT RgCgMgOhZ RfW[X RlWj[k[',
	    'PoSI\\I R^ZiZ RXCX[ R^R^[ R^RiRi[ RWK[P R`IfOlQ RWJRU R`FiFgJbO[R RaC`G]J',
	    'PoTF_F RRKlK RSP`P RZCZK RZMZ[ RZS^V RgDkH RjO`Z RYPSX ReYj[ RlWk[ RcCcKeY',
	    'PoSFkF RRSlS R_C_F R_P_[ RbFbO RfHkM RWISM R[KWP R`ScVlZ R_R[VRZ R_ObO_O R\\F[M',
	    'PoSI[I R`LlL RWCW[ R_H_[ RWJYP RgSkZ RiEkI RWJRT R`D[O RfCfNiY RWHTS RePcY R_F^M RfKdW RfIhV',
	    'PoaFkF R]MlM RRSlS R_P_[ RaFaL RfCfQ RWIZL RUIXJ RXERJ R`SdWlZ RWG^F\\KXORQ R^SZWRZ',
	    'PocEkE RSIYI RbLlL Re[h[ RVCV[ R_L_[ RiLi[ R`D[I R`I_M[P RVJYQ RVHSU',
	    'Po]HjH RSI[I R\\NlN R\\SkS RZZlZ RWCW[ RdCdM RdPdZ RYLZP RWJRT RWGTS',
	    'PoRIZI Rf[l[ RWCW[ RfCf[ RlXk[ RVJZN RgPlT R^R[T RaQ^R R_W[[ R\\H^L RjGhL RWHSU RaCaQ`W',
	    'PoRI[I R[NlN Rf[l[ RWCW[ RdCdN RgNg[ RlWk[ RWM[P R_W[[ R]E_K RjEhK RWHST RaN_X',
	    'PoSI\\I R]KlK RfUiU Rb[k[ RWCW[ RbNbZ RbNjNjT RWKZN RlWkZ R^I[L RWJRT R`FgFeJ RWGTS R`C]J^S\\Z',
	    'Po]ElE R`IkI RRJ[J R`OjO R_VkV R]ZlZ RWCW[ R]E][ ReIeU RYNZQ RWHTT',
	    'PoaNjN RRSlS R_Q_[ RaFaN RaFjFjN RUK^O R`ScWlZ RXCTK RRG^G\\KYNSQ R^SZWRZ',
	    'PoSJkJ RRTlT RSFSH R_C_F R_Q_[ RSFkFkH R\\HWN R`TcWl[ R_S\\VR[ RXMjQ ReJaOTQ',
	    'PoSI[I R\\NlN RWCW[ RWM[P R_TlZ RWKRU RhOfUcX][ RcCcG RWHTS RbJ_T R\\G\\K R\\GlGlK',
	    'PoWHgH RRMlM RVShS R_O_[ ReDjI RYDTI RbHcMhQlR R_RcVjZ R^D[LVQRS R_RZWTZ',
	    'Po\\FlF RRIZI R]NjN RWCW[ RVJYP RdCdReVhYl[ RbU^Y[[ R]JjJjN RWHST R]N]RkRjWhV RcMbU',
	    'Po_FkF RVMhM RVPhP RRTlT RVIVP R_C_I R_P_[ RVIhIhP R`TcWl[ R]T[WR[',
	    'Po\\GlG RRI[I R_MhM R\\QkQ Rc[g[ RWCW[ RWM[Q RfHlM RaH\\N RWJRU RcC`J RWHTS R`Q^UiUg[',
	    'Po\\ElE RRI[I R^MkM R\\MfM R]SkS R[ZlZ RWCW[ RdOdZ RWM[Q RfIlN RWJRU RbE^M RWHTS',
	    'Po[FcF RSJ[J RWCW[ R_F_T ReDe[ RWJYNZQ ReElEhM RWKRU RWHTT RcS[U RjNlSkWgW',
	    'PoRI[I RaIiI RbUhU Rh[l[ RWCW[ R^E^[ RbNbV RbNhNhU R^ElEl[ RWIZP RWGTS',
	    'PoVEgE R`LjL RRTlT R_R_[ R`TcWl[ RSL\\LZNRQ R]T[WR[ RaNkQ RTN\\P RiMeO_P ReF^HUJ RWFfI',
	    'PoUFiF RRLlL RRSlS RXIXP R_C_J R_O_[ RfIfP R_RcVlZ R_R[VRZ',
	    'Po]ElE RRI[I R_OjO R_UjU R\\ZlZ RWCW[ R_I_U R_IjIjU RYLZP RWIYN RWJRT RWGTS',
	    'Po\\HlH RRIZI R]NkN R^ZjZ RWCW[ R^R^[ RdCdN R^RjRj[ RWKYMZP RWHSS',
	    'PoSI\\I R^PdP RfPlP R^YlY RXCX[ R^G^[ RfGlGl[ RXM\\Q RXIRV RdD^G RXGVO',
	    'PoRJ[J R[ZlZ RWCW[ R]H]Z RbGbZ RfHfZ R]HjHjZ RWM[Q RWKRU RWHTS RbDbL',
	    'PoSI[I RfOjO R[RkR RWCW[ RcNc[ RWK[O RcQkY R^WYY RbR\\Y RWKRU R]N[N RWHTT R\\EfEeIkHjN R`E^M',
	    'PoSI^I R`NkN R`ZlZ RXCX[ R`G`[ R`GkGkM R`RlRl[ RXM]R RXISU ReCcG',
	    'PoRI[I R]JkJ R^PkP R\\UlU RWCW[ ReKe[ RWM[P RgFlK RWJRU RbC_J R`L]R RWHTS',
	    'PoRIZI R`PkP RWCW[ R]H_L RbHeK RhHlL RbN]S RjEiH R_D]H RWJYP RaTfV ReDcH RjReW\\[ RWHST',
	    'PodGkG RRI\\I R]MlM R]PlP RXCX[ RdCdL RdPd[ RXM\\Q ReSkV RXIRV RXFVO',
	    'PoRI[I R\\PlP RWCW[ RWM[Q R\\E^J R_Uk[ RlDhL RWJRU RbL^V RcDdJ RWHTS RiPgUdXaZ[[',
	    'PodIlI RRIZI RbSlS RWCW[ RdIdS RiCi[ R_W[[ RVKYQ R]OaQ]Z RWHSU RbL_W R^D[O R]HbI`V',
	    'PoSI[I RWCW[ ReEe[ R_W[[ RWIYO ReElEhM RWJRT R^NaQ]Y RkWgW RWHTR R^E[Q RcK_W R^HcHaU RiNlTlV',
	    'PoRI[I RWCW[ RWM[Q RfDjE RWJRU RiSfW]Z RkPfP]R R^HiH R^MiL RbCbJdTeWk[mX',
	    'Po\\FlF RSJ[J R[YlY RWCW[ RaFaY RfFfY RWJYO RWJRT R\\K^T RkKiT RWHTR',
	    'PoaGhG R_GeG RSIZI R[RlR Rf[l[ RWCW[ RfRf[ RkLkO RlXl[ R`W\\[ RWIZO R\\HbGcKfNiO R`CbK RWGST RaR_Y RhJcM\\N RkFeG',
	    'PodFlF RSI[I RWCW[ R^J^V RdCdJ R^JjJjV RWKZN ReXjZ RcV`Y\\[ RWGSS RdMdRcW',
	    'PoRI\\I R]SlS R\\YlY RXCX[ ReCeM R]MlMl[ RWK\\P R^F`K RkEiK RXJRU RXHTS',
	    'PoRIZI R\\JkJ R]SjS R][k[ RWCW[ R\\D\\I R]S]Z RdCdI R\\NkNkS RkDkI RXLYP RlXk[ RVIXN RVHST',
	    'Po^EdE RRI[I R\\JlJ RWCW[ RhQh[ RWIYN RfKlQ RaL\\Q RWJRT RcF`N RWGTS R`Q`X RiD`E',
	    'PoSI[I R\\TlT RWCW[ R_F_P RdQd[ ReCeOkNlO RWIXMZO R`C]J RjEfJaL RWHST',
	    'PoRI[I R_MiM R]QlQ RWCW[ R\\ZkYk[ RWM[P RgVkY ReDlL RcE\\L RWJRU RbQ_Z RWHTT',
	    'PoSSkS RZCZO R_P_[ RTEVI RaIdL RcC]I R`SdVlZ RYJSM R]SZVRZ R`FkGgKcM\\O',
	    'Po^HlH RRI[I R`PkP R_ZlZ RWCW[ ReJeZ RWIYN RcCeE RWJRT RWGTR R^H^R\\Z',
	    'PoRI[I R]IjI R]NjN R[RlR Rc[h[ RWCW[ RhPhZ R]EjEjN RVIYN R^UaX RWHST',
	    'PoRIZI RcSfS RWCW[ RfCfR RWMZQ R\\D_E RZK^M R_R[[ RjQhVeYa[ RjHlO RWHSU RbH`O',
	    'PoRSlS R_P_[ RcCcJ RgEkI RXKSO R^E\\I RUDYE RRGWH R`ScVlZ R^SZVRZ RgJbMZO',
	    'Po^EkE R`IjI RSI[I R^OkO RWCW[ RaNaZ RhWl[ RWM[Q RfRiY RWJRU RkRfS ReNhX RWHTT RgY^Z R^E^Q\\Y',
	    'Po\\ElE RSI\\I R^YjY RXCX[ R^R^[ RdGdP R^RjRj[ RXM\\Q RiKlM RfJjK RWJRU RfE`K[M RWHTS',
	    'PoSI\\I R^LjL R^UkU R\\[l[ RXCX[ R^E^L R^PePeZ R^EjEjL RXM[P RWJRU RWHTS RjOaP',
	    'PoRI[I R^JlJ R^ZjZ RWCW[ R^E^P R^R^[ R^RjRj[ RlNkQ RWMZP RgOkP RWKRU RWHTS RdEfN ReN\\P RiD^E',
	    'Po\\ElE RSJ[J R\\SkS RZ[l[ RWCW[ RdSdZ R\\L_O RaLeP RfLkP RjHfK R_H\\K RdHaK RXMZQ RWKRU RWHTS',
	    'Po\\GlG R_OiO R\\UlU R_[d[ RXCX[ R\\K\\O RdCdK RdRd[ RRI\\JlKlO RXM\\Q RhPdR RXIRU RXGVP',
	    'PocIjI R`IgI R]IdI RSJZJ R\\NkN R^UhU RWCW[ RcQc[ RWKYN ReNkU RfEkI RbD\\I RbN[T RbJ_Q RWHST',
	    'PoRI[I R^MjM R[TlT R`[d[ RWCW[ RdQd[ RWM[Q RdGdK RjGkK RWJRU R_E\\L RiOdQ RWHTS RjD_E',
	    'PoRI[I R[PlP RXCX[ RXN[Q RcGcK RiGkL R^Uk[ RWJRU RbL^V RWHTS R^E\\M RiPgVdXaY[[ RiD^E',
	    'Po]ElE RSI[I R]PlP R]UlU Ri[l[ RWCW[ R]K][ RdKd[ R]KlKl[ RWKZN R`GiK RVLRU RkFdH RWGTS',
	    'PoRI\\I R_OlO R^UlU Rh[l[ RXCX[ RfJfT R]JlJl[ RXM\\Q RXIRU RaFhFeK RXFVP RbC`H]J^S]Y',
	    'PoRIZI R[LlL RgYjY RWCW[ R^P^Y RdNd[ R]RjQjX RVKZO R`N[S RbI_P RiD^I R]DkI RVGSR',
	    'PoSI\\I R^KjK R^PjP RXCX[ R^G^Z RdCdF R^GjGjP ReUjZ RXM\\Q RcPfX RWJRU RjTfV RWHTS ReY[[',
	    'Po\\ElE RRI[I R\\JaJ R\\SaS RfSlS RWCW[ R\\J\\S RfEf[ RfJlJlS RWM[Q R`W\\[ RVLRU RWHTT RaEaQ`W',
	    'PoSI[I R]PkP R^TjT R\\WlW RWCW[ RdNd[ RaE\\J RWJYP R`GdLlO RVLRU R`FiGdK\\O RWHTS',
	    'PoRGlG RXS[U ReScU R`GcJlN R`NkS R]GZKRN R_C_L\\ORT RaWiY R]WTY R_P_[[[',
	    'PoSGlG RVVhV RR[l[ R_C_M R_N_[ RYQ\\U RgRjU RfPbU RXPSU RaHlM R]HRM',
	    'PoSI[I R]JlJ R]NjN R`ZjZ RXCX[ R`Q`[ R^EjEjN R_SjSk[ RXM\\Q R_R[V RWJRU RbL^U RWHTS RcE`R',
	    'Po^IjI RRI[I R^MjM R]PkP R[UlU RWCW[ R^D^M RdPd[ R^DjDjM RWM[Q RWJRU RWHTS',
	    'PoZGlG RRIZI R\\QjQ RWCW[ R\\K\\Q RcCc[ R\\KjKjQ RdRkZ RbS[Z RVJYQ RWHST',
	    'PoRSlS R_P_[ RXKSO R`J\\O RiGkL R_RcVlZ R\\GZK R^SZWRZ RTDXD RRHWH RbE`L R[DhDhIfOcO',
	    'Po\\HlH RRI[I R_[c[ RWCW[ RcCc[ RWM[Q RfTlY RkKiO R]L_P RdNhV RgDkF RWJRU R`U[W RWHTT RcHfS',
	    'PoRJZJ RbMlM RbUkU Rb[l[ RWCW[ RgEgU R^WZ[ RVKYQ R_R\\Y RWISU R[E`E[N`M^W RkDbF R\\R^WaZhZ',
	    'Po\\ElE RSI\\I RfQlQ R]VlV R]ZlZ RXCX[ R]K][ RbEbO RgEgQ R]KlKl[ RXM\\Q RXIRU RXGVO',
	    'Po_HlH RRJ[J R]XlX RWCW[ RWMZP RWKRU R`C\\K R_K]Y ReKeOdRcW R[QkQ R_LjLjWi[f[',
	    'Po[GcG RRI[I R[McM RWCW[ ReEe[ RWM[Q RjNlT RVMRU ReElEiN RWHTT RcSZT RlWhW R_C_R^X',
	    'Po]ElE RRI[I R^JjJ R\\NlN R[TlT RWCW[ RdEdN RhPh[ RWM[Q R`P`W\\[ RUPSU RWHTT',
	    'Po\\FlF RSI\\I R^NiN R_[d[ RWCW[ R^I^N RdCdG RdTd[ R^IjIjN RWM[Q RWJRU R^QjQdT RWHTS',
	    'PoRJYJ R\\KaK RcLlL R[PaP RcYkY RVCV[ R\\E\\K RaCa[ RgCgY RUJYQ RVFST R]P\\Z',
	    'PoRG]G R_JlJ RTPXP RRSlS RXCXP R_Q_[ RgIgP R`ScWlZ R]SZWRZ R_F^O RiD`E R\\JRL',
	    'PoSI\\I R_JkJ R^RlR R^VlV Rh[l[ RXCX[ R^M^[ R_D_J R_DkDkJ R^MlMl[ RXM\\Q RXIRU RXFVP',
	    'PoSI[I R^KjK R\\QlQ R`[d[ RWCW[ RdKd[ RVIYN RiTkY RdDgHlJ R^S\\X RdC`H\\J RWHST',
	    'PoSI\\I R^KjK R^OjO Ra[k[ RXCX[ R^F^O RaQa[ RcCcF R^FjFjO RXM\\Q RlXk[ RWJRU RWHTS RdT[U RfT]U RjS`U',
	    'PoRI\\I R^KkK R^OkO R_[k[ RWCW[ R^F^O R_Q_[ RcCcG R^FkFkO RWM[Q RlXk[ RVMRU RWHTT RjR^V',
	    'Po_HkH RSI\\I R]NlN R_ZkZ RXCX[ R_R_[ ReCeQ R_RkRk[ RXM\\Q RXJRU RXHTS R`D^K',
	    'Po]HcH RSI[I R]TjT RZ[l[ RXCX[ R]D]O RcPcZ RfDeNlMlO RXM[Q RWJRU RkFeI RWHTS RcN[N',
	    'PoRIZI RWCW[ R_K_Y RXNZR R_GZK RfCfSkZ ReRbZ R\\D_L\\S RbHbO RfMiY RjHiO RWHST RfMdW R^[[[ R`C^H_Q RfJhV',
	    'PoSI[I R]ZiZ RWCW[ R]S][ R]SiSi[ RbIeK R]NiNdR RdC\\J RWJZQ RcDhIlJ RWHST',
	    'Po]GlG RSI[I R[NlN R\\TlT RWCW[ RdCdG RdNd[ RWN[Q R_I`M RiHgM RWJRU RWHTS',
	    'Po]FdF RRJ[J R]JlJ R`SkS R`[k[ RWCW[ R`M`Z ReMeS R`MkMkS RWM[Q RlWk[ RWJRU RWHTS R]F]R\\Y RjD`F',
	    'PoRI[I ReOiO R`[g[ RWCW[ RaSa[ RhXg[ RWM[Q RaPeQ RbJ^O RiTlY R_G]K R]T[Y RWHST RdEbL R^DkDkJjO',
	    'PoSI\\I R^JjJ R^PjP R^UjU RXCX[ R^D^U R^DjDjU RhXk[ RXM\\Q RXIRU R`X[[ RXGVP',
	    'Po\\ElE RRI[I R\\MkM R\\RkR RWCW[ R\\I\\R R\\IkIkR RbU_Y RWIYN RWJRT R]TaXZ[ RaXj[ RWGTR RcEcRbV',
	    'PoRI\\I R^IjI R^NjN R^SjS Rf[l[ RXCX[ R^E^S RgSg[ R^EjEjS RlWl[ RXM\\Q R`W\\[ RXIRU RaT_Y RXGVO',
	    'PoSI\\I R^MkM R]VlV RXCX[ R]P]X R^G^M RdCd[ R^GkGkM R]PlPlW RXM[Q RXIRU RXGVP',
	    'PoRI[I R]RkR Re[l[ RXCX[ R^J^R ReReZ R\\KkKkR RlXl[ RXM[Q RbV][ R^H[L RXIRV R`C]K RXGVP R`FgFdKbV',
	    'PoRJ[J R[KcK R[RcR RWCW[ R[DcDcY RfDf[ RVLZO RWHST RfElEiMlRkVhV Rb[_[ RkNlT R^D^P\\Y',
	    'Po[HlH RRI[I RWCW[ R^NaQ ReUj[ RWK[P RhNlR RdCcQfX RhKeR R^L[S RcPaV]YZ[ RWHTT RcMdU',
	    'Po`GlG R]RkR RWHW[ R[F[V RdOd[ RdRkZ RdR]Y R`F\\J R_GdLkO RXDTN RaC_H RhHdL]P',
	    'PoRI[I R^JiJ R^PkP R`[h[ RWCW[ R_J_O RaSa[ RhXh[ R^EiEiJ RbQeT RWM[Q RkLkO RiTlZ R]T[Z RWHST',
	    'PoVHhH RVJhJ RVMlM ReSiS RRTlT R_R_[ RVEhEhJ RVEVOjPiR R]C]F RaVkZ R]VR[',
	    'PoSI]I R_LkL R^ZlZ RXCX[ R^Q^[ R_D_L R_DkDkL R^QlQl[ RXM]R RXIRV ReLdQ',
	    'PoSI[I R^KiK R\\PlP Rf[l[ RWCW[ R]G\\J RfPfZ R]GlGlJ RlWk[ R`W\\[ RWKYOZR RWJRU RdCdG RWHTS RaP_Y',
	    'PoRI\\I R^PkP R^UkU Rf[k[ RXCX[ R^K^[ ReCeK R^KkKk[ R^D`I RkFhH RYLZP RXIRT RWFVO',
	    'Po]IkI RRI[I RXCX[ R]D]J RkDkJ RWK[N RdCcIjO R_OhOlP RdI]P R]TjTf[ RWJRU RWHTS',
	    'Po^IkI RRJ[J Re[i[ RWCW[ R^E^I ReDeI R_QiQiZ RkEkI RgLlQ RbL]Q RbW^[ RWJYNZQ RWKRU RWHTT RcRaX',
	    'PoUPhP RSTlT RSFSI R_C_F R_Q_[ RSFkFkI R\\MYP R\\IdIkM RbMhO RaUkZ R]URZ RYJSM',
	    'PoRF]F R_FlF RXCXN ReCeN ReFkM RYFRM ReF_M RWFYI]J R]PST R[QhQcVZZ RYRaWT[',
	    'Po]DkD RSI\\I R\\OlO R^ZjZ RXCX[ R^R^[ R^IiIiO R^RjRj[ RXM\\Q RWJRU RWHTS RbD`O',
	    'PoRH_H RePjP RRSlS RSEZDZP R_Q_[ RdEdM RjCjP RYH]L RYHRO R`ScWlZ R^SZWRZ R^CVD',
	    'PoSI\\I R^JkJ R^PkP R]UkU R\\[l[ RXCX[ R^E^P RdEdZ R^EkEkP RWL[O RXIRU RXGVP',
	    'Po]ElE RSI\\I R_QjQ R\\[l[ RXCX[ R_J_Q R_JjJjQ RXM\\Q RiSgY RXIRU R`SaY',
	    'Po\\HlH RRI[I R^LiL RWCW[ R[O[R R^DiDiL R\\OkOkR RWM[P RWJRU R_ScXfZl[ RWHTT R]SiSeXaZ[[',
	    'PoRIZI R]JjJ R]MjM R[QlQ Rc[h[ RWCW[ R]F]M RcCcM R]FjFjM RVIYP R_Q^UjUh[ RVHST',
	    'Po\\IkI RRI[I RWCW[ RgElJ RbO\\U RYNZQ R`SdXl[ RWKRU RbC_I RaI`M\\O RgIgNlMlN RWHTT R`RiReW`Z[[',
	    'PoSI[I R]JkJ R`WhW Rg[k[ RWCW[ R`Q`Y R`QhQhW R]DkDkJ RYMZQ RVLRU RWHTS R]NlNlVk[ R]D]R\\Z',
	    'PoRJ[J R^NkN RgYkY RXCX[ RdHd[ R]HkHkM RWL[P RiChF R_W[Y RdR]X R`CaG RWJRU RWHTS R^M]SlRkY',
	    'Po[IlI RSJYJ R\\QdQ RWCW[ RbKbY RmXk[ RhDjF RVJYP RkMe[ RVISU RfCeKi[ R^K^R]X Ri[k[',
	    'PoRI[I R_KjK R\\YlY RWCW[ R\\D\\[ RdGdW R\\DlDl[ RYNZR RgQkT RdJiS RdJ`T RWHST',
	    'PoRI\\I R^RiR Rf[l[ RXCX[ R^K^R RfRf[ R^KiKiR RlXl[ RXM\\Q RhGlK R`EgEjJ R`W\\[ R`G\\K RXIRU RaR_Y RXFVP',
	    'Po\\GlG RSI[I R\\MjM Ri[l[ RWCW[ RdPdZ RdCdG RiPi[ RlXl[ RWMZP RgJkN RWKRU RbG_M RWHTT R_Q^Y',
	    'PoRIZI RgMkM RbVlV Ra[l[ RWCW[ RcJcV RgEgV RXLYO R]WY[ RWHSU RWFTS RZE_E[N`M]X RkDaF R[R]XaZfZ',
	    'PoRG]G R_GlG Re[l[ RXCXN RYPePeZ ReCeN RlWk[ RXG\\K ReGkM RXVT[ RWIRM ReG_M R[SaV RXEVJ RYPXW',
	    'PoRJ[J R]JkJ Rd[l[ RWCW[ ReMe[ R]EkEkI RlXl[ RWM[Q RWJRU RWHTS RiL_M R]E]Q\\Y RjP_R RkT`V',
	    'PoXG^G RSP]P RSTlT RSJSP RXCXJ RSJ]J]P R_Q_[ ReJlP RaUlZ RaEkEiJfM`O R]VRZ',
	    'PoRJ[J R[KdK Rh[l[ RWCW[ R`E`[ RfFfU RlCl[ R`NdS RWMZP R`K[U RWHTT RdD[F',
	    'PoSI\\I R_KkK R[TlT RXCX[ R_F_T RgKgT RXM[Q RgVl[ RaV[[ RWJRU RWHTS RiD_E',
	    'PoRG]G R_GlG RRPlP RXCXN R_P_[ ReCeN ReGkM RYGRM ReG_M RWGYI]K R_SjV',
	    'PoRIZI R`MkM RWCW[ R]H][ RhDh[ R`GkGkM RVJYQ R_D[L RWHSU R_RlRlXiX R`M_S RdDdRbX',
	    'Po\\GlG RRJ[J R]JkJ R[NlN R\\RlR Rd[h[ RWCW[ RhNh[ RaUdX RXNZP R`S[X RcCcM_V RWHTT RcGaR',
	    'Po\\HlH RRI[I R]NkN R`ZgZ RWCW[ RaRaZ RdCdN RhWgZ RXLZP RbPfS RWIYN RiSlY RWIRT R]S[Y RWFUO',
	    'Po]GkG RRI[I R\\PlP RWCW[ RdCd[ RVKZN ReRlX RdNbR\\X R^J`N RdNfS RiIgM RWHSS',
	    'Po`FkF RRIYI RaMkM RaRlR RbZlZ RVCV[ RfIfX RVJXP R\\E^H R[M^M^W[Z R^XdZ RVHRU RdDaM RVFUQ',
	    'PoRI[I R_LgL R[ZlZ RWCW[ RcDkM RcCbG[M RVIYP RjOeZ R]P^V RWHST RcOcU',
	    'PoRI[I R^QjQ Rf[l[ RWCW[ R^I^Q RgQg[ R^IjIjQ RlXl[ R`W\\[ RWIYMZP R_CaG RWJRT RiCgI RWGTS RaQ`X',
	    'Po^IjI RSI[I R]MjM RWCW[ R]EjEjM RWIZO RcSeVgYl[ RjQhU RWJRT R^Q\\U RbV_Y[[ RWGTS RcObW',
	    'PoRH^H RfOiO RRSlS RYDYO R_P_[ RdK`O RYHRN RYJ]L R`SdWlZ RbC_J RfFcM R^SZWRZ RaGkGjO R\\CSE',
	    'PoRFlF RSJjJ RRNlN RWQgQ RRTlT RWLWQ R_C_F R_K_[ RgLgQ R\\FXJ RaVkZ RcGkK R]VR[',
	    'Po\\HlH RSI[I R[OlO R\\SjS RWCW[ R]EdEc[ R]KjKjS RhXlY RcRjY RWM[P R^XZY RcR\\Y RWKRU RWHTT RiDbD',
	    'PoRI[I R_KhK R]TjT RWCW[ R]O][ RaOa[ RfOf[ RdCkJ RcC\\K RYNZQ RUPSU RWHTT R]OjOj[h[',
	    'PoRIZI RbOiO R^OeO R`[h[ RWCW[ R`U`[ RgXh[ RhUlX RfKiN RfEkJ R`E[J RaK]O RVIXMYP RaReT RbH`M R\\UZZ RVHSU',
	    'Po\\FkF R]IjI RRI[I R\\LlL R^UjU Rf[j[ RWCW[ R^P^[ RdCdL RdPdU R^PjPj[ RWM[Q RWHST',
	    'PoSI\\I R_JlJ R_NlN RiYlY RXCX[ R^R^Y R_F_N RdCcF ReNe[ R_FlFlN R^RlRlY RWK\\P RXJRU RXHTS',
	    'PoSFkF RYIeI RYLeL RRPlP RWUgU R_R_[ RfCePhSlU R`UcWj[ RYCYPUTRU R^U[WTZ',
	    'Po\\GlG RSI[I R`LiL R`PiP R\\TlT RWCW[ R`C`T RiCiT RgVl[ RWIYO RaV\\[ RWGST',
	    'PoRJ[J R\\QkQ RWCW[ RcMc[ RWK[O ReSlY ReQfU RbS[Y RcGdK RiGkL RWJRU RWHTS R^E\\L RiD^E',
	    'Po]IjI RRJ[J R]NjN R^TcT Re[l[ RWCW[ R]E]N R^O^Z RfOf[ R]EjEjN RlXl[ RWM[Q RWKRU RkReU RWHTS RcY[Z',
	    'PoSI[I R\\QkQ RWCW[ R\\E\\H RcNc[ R\\EjEjH RdRkY RcQfS ReHjM RbR\\X RaH\\M RWIYMZP RWJRT RWGTS',
	    'Po^HjH RRI[I R^LjL R\\OlO R[SlS Rc[g[ RWCW[ R^D^L RgOg[ R^DjDjL RWM[P R^UaX RWHST',
	    'PoSF\\F RbFkF RTI\\I RbJkJ RbMlM RRSlS R_P_[ RbCbP R\\C\\MWQ R`ScVlZ R^SZVRZ R[LRN',
	    'Po\\HaH RfHlH RSI[I RfNkN RfTlT RWCW[ RfCf[ R`W\\[ RYNZQ RWJRU RWHTR RaCaTZT R\\NaN`W',
	    'Po\\FkF RRI[I R]JjJ R[NlN R_ShS R]WjW RWCW[ RdPd[ RWMZP RhOlS RaN[T RfJiQ RUOSU RbI_Q RWHTT RbDaM',
	    'Po]GkG RSI\\I R\\NlN R^ZjZ RXCX[ R^R^[ RdCdG R^RjRj[ RXM\\Q R`H`M RhHgM RWJRU RWHTS',
	    'PoRI[I R^JlJ R^VjV R^ZjZ RWCW[ R^E^O R^R^[ R^RjRj[ RWM[Q RlMlP RgOkP RWJRU RWHTS ReEfM RdN\\O RiD^F',
	    'PoSI[I R_LjL R]QlQ R`[d[ RWCW[ R]G]J RdQd[ RdCdG R]GkGkJ RWMZP R_T]X RiTkY RWKRU RWHTT',
	    'Po_EjE R_HiH RRI\\I R_LiL R\\PlP RXCX[ R_E_Z ReUjZ RXN[Q RcPfW RXIRU RiTeU RXGVP ReY\\Z',
	    'PoSEkE ReKiK RRPkP ReWiW RUSUW RUHUK R_C_M R_N_[ RUHiHiK RUSiSiV R_IdLkN R_TaVfYlZ R_IZLRN R_T\\WWYRZ',
	    'PoSG]G R`GkG R[Q]Q RhRkR RSKSR RXCX[ RSK^K^Q R`K`R ReCe[ R`KkKkQ RZS]W RXQ[U RhUlY RdS^Y RVURY ReOiV ReOcU RXOUW',
	    'PoRJ[J R]NkN R_SlS RWCW[ R]IkIkN RePfWj[ RWM[Q RhPjQ ReSgY RWJRU ReScWaY^[ RWHTS R]E]Q\\X RiD_E',
	    'PoRIZI R\\KbK RfKlK R\\RbR RfRlR R^[b[ Rh[l[ RWCW[ R\\EbEb[ RfElEl[ RVIYP RVHSU R\\E\\Q[Y RfEfTd[',
	    'Po\\FlF RRIZI R`LkL R\\LhL R^RjR R^VjV Rf[i[ RWCW[ R^O^[ R^OjOjZ RVIXMZP RcCdF RgHkM RbF\\L RWHST',
	    'Po[HcH RSJYJ R][a[ RWCW[ ReUj[ ReNiQ RVKYR RgCkL RgCcL RWHSU R_C^H]O[X R]NbNbVa[',
	    'PoRJ[J R^KiK R`VhV R\\YlY RWCW[ R\\E\\[ R`P`V RdGdO R`PhPhV R\\ElEl[ RXMZQ RWHSU',
	    'PoRIZI R_MkM R]ZlZ RWCW[ R]E][ ReHeW R]ElEl[ RVK[O RhQiU RdNiR RcQ_U RWIRS ReKaS RWFUO RiG`H',
	    'Po\\GkG RSI[I R]NjN R]SjS RWCW[ R]J]R RdCd[ R]JjJjR RWM[Q RdRkY R_X[Y RdR]Y RWKRU RWHTT',
	    'PoXPfP RSTkT RTHTL RXKXP R_C_H R_P_[ RXKfKfP RTHkHkL RVDYF RhDeF R_SbVkZ R_S\\VSZ',
	    'PoRJ[J R_LjL R`ViV Rh[l[ RWCW[ R]E][ R`O`X RdLdV RiOiW R]ElEl[ RWMZP RaGaJ RWHST RgFgJ',
	    'PoSI[I R`KjK R`PiP RaViV Rh[l[ RWCW[ R]D][ RaPaV R]DlDl[ RWIYMZP RdMeO RbGbJ RWJRU RgGeK RWGTR',
	    'PoRJ[J R\\KlK R]OjO R_[c[ RWCW[ RcCc[ R]FjFjO ReTjY R]Q`T RWM[Q RjQfU RcOfV RWKRU RaV[Y RWHTT',
	    'Po]GkG RSIZI R\\MlM R^UjU R^ZjZ RWCW[ R^P^[ RaCaM RgCgM R^PjPj[ RXLYO RWGSS',
	    'PocIkI RRJZJ RWCW[ RgCg[ R\\RbW R[G_HbL R_TZY R_I[N RWJYP RaD^L RaO]W RgGkS RWISU RgHdT',
	    'PoRIZI R\\OlO R^TjT R`[d[ RWCW[ RdJd[ R]KjJjU R`FgFeJ R`G\\K RWIYMZP RUORU RaC^I RWHSS',
	    'Po\\GlG RRI[I R[SlS RWCW[ RkYl[ RlLkN RYNZQ R`DcJeMjN RdOhQ R`PbVeYj[ ReDhE RWKRU RWHTT RhIcL\\N RhVcY[[',
	    'PoTN^N RRSkS RUH^H^M R_Q_[ RbGcKgNlP R`ScWkZ RcC_K RbGjGhKdN`P R]SZWRZ RUEUKSQ R]DTE',
	    'PoSI[I R]KbK RgKlK R]OlO RgRlR RWCW[ RbDbK R]D]TcRc[ RgDgK RgRg[ RlDl[ RWM[Q RWJRU RWHTS R]O\\Y',
	    'PoSI[I RWCW[ RWMZP RiFhI RiQhU RcJkN RdVgYl[ R_E^I R_R]V RcJ`M\\N RcSbW`Y[[ RWHTT RdPdW RdCcK',
	    'Po[HlH RRJZJ R\\SbS RWCW[ R\\L\\S R\\LbLbS RXNYP RhDkE RgUa[ RkLfW RWHST RbVZX RlWk[i[ RfCfNgY',
	    'Po^IiI RSJZJ R[MkM R_[j[ RWCW[ R_R_[ RfMlS RhEkH R`MZS RjXj[ R^E\\I RYMZP ReIhO RbG^P RgVcV R_RgRgW RWHST RcD`N',
	    'Po[GlG RRI[I RWCW[ R^L^[ RcCcG ReTk[ RWKZN ReNgQ RhTaZ RbNfW RcLjMfV RUORU R_I[R RdI`S RWHTT',
	    'PoTGjG RSR]R R_RkR RXNX[ R_C_N ReNe[ RZT\\W RWR[U RfTlY RcT^Z RVTRY R_FbJjM RePcU RXPVV R_F[JTM',
	    'PoRJ[J R`JgJ RWCW[ RaUa[hZh[ RaReT RWM[P RdCjJ RcC\\J RjVlZ R^MiMfR R]U[Z RWHTT',
	    'PoSI[I R[OlO R\\TlT R[ZlZ RWCW[ R_J_T R]FdEdZ R\\JjJiT RWM[Q RVLRU RWHTT RjDaE',
	    'Po]FjF RRI\\JlK RWCW[ RdCdK RfKfOkNkP RWN[Q RbP\\V R_SdXj[ RWJRU RaK_O\\P R`SiSdW][ RWHTS',
	    'Po\\FlF RRI[I R[LlL R]PjP R[SlS RWCW[ RdCdO R]IjIjO RWMZP RWKRU RbP^WkZ RWHTS RhUdY[[',
	    'PoRIZI RWCW[ RfVk[ R^UbY RXNZR R`IcL RhIlM RiHdN R`V[[ R`J\\N RdRgX R]HaJ R\\T`V ReHiJ RdDkDhK R\\DbE_M R[QbQ^X RcQkQiVeYb[ RWHSU',
	    'PoRJYJ RgKjK R\\KaK R\\RaR R][a[ RVCV[ R\\DaDa[ RdDd[ RdDkDkJ RVKYQ RfPjY RkPgZ ReNhW RdNlNiW RVIRV RVFTR R\\D\\SZ[',
	    'PoRI[I R^IjI R^NjN R\\RlR RWCW[ R^D^N RdDd[ R^DjDjN RWIYMZO ReSlY RcS\\Y RWJRT RWGTS',
	    'Po[GlG RSI[I RWCW[ RcCc[ R^MaP RVKZO RcQlY RhMlP RcObS[Y RhJfP RVLRU R_J\\Q RWHTS',
	    'PoZEkE RRIZI R\\QbQ RWCW[ R\\E\\W RbEb[ RgVl[ RVLZP RhWc[ ReQiY RjPeZ RWHSU ReJgW R\\KbJkIhW RbVZW',
	    'Po`FkF RUH\\H RUK\\K RUO\\O RRSlS RUDUO R_Q_[ RfKjO RaIfK R_RbVjY RjGeM_O R_R[VSZ RYNRO RRD^D^OXN',
	    'PodFlF RSI[I R]NjN R]RjR R[VlV RWCW[ R]J]R RdCdJ RdRd[ R]JjJjR RYL[O RWJRT RWGTR',
	    'PoRI\\I R_PjP R_ZkZ RXCX[ R_K_[ ReCeG R_KjKjP R_TkTk[ RXM\\Q RWJRU RWHTS R]G]J R]GlGlJ',
	    'PoRElE RaQeQ RSTlT RZCZG R_R_[ RdCdG R\\IdJkM RaVlZ RXMfMeQ RYJSM R^M[PVR R\\VR[',
	    'PoRF]F R_FlF R`[e[ RXCXN ReCeN RfGkM RYFRM ReF^M RWFYI\\J RYPdPgSlU RXRRU R]UZYT[ RWTfTe[',
	    'PoSF]F R_FkF RYReR RXCXM ReCeM ReFkL RYFSM ReF`K RWFYI]J R_MkS RVUfUc[ R]NRS',
	    'PoRF^F R`FlF Rd[k[ RSOSS RXCXM RYRdRd[ ReCeM RSOkOkS RkWk[ ReFkL RXFRL ReF_L RWFYH]J RXYS[ RYRXX',
	    'PoSI[I R]KbK ReKjK R]QjQ Re[l[ RWCW[ R^E^P RfQf[ ReEjEjP RlWl[ R_V[Z RWJYP RcD]E RWGST RaQ_X',
	    'PoRI[I Re[l[ RWCW[ R\\G\\J RdCdG RfLfZ R\\GkGlJ RlXl[ RVLZO R`V[[ R\\QaS RbQ_Y RWHST R^J\\R R^NcM`W ReLkLjTgT',
	    'Po]GlG RSI\\I R_NjN R\\VlV R`[d[ RXCX[ R_I_N RdTd[ RdCdG R_IjIjN RXM\\Q RXIRU R^RjRdT RXGVP',
	    'PoRI[I R^IiI R\\MlM R`[c[ RWCW[ RdMd[ R]EiEiM R]P`S RWKZN RjPgS RdRhWlX RVLRU RcT\\X RWHTS',
	    'PocFkF RRI[I RbNhN R`NfN RcQkQ Rf[l[ RWCW[ RcKcQ RcCcI RgSg[ RlXl[ RWN[Q R\\IkIkM RWJRU R`Y\\[ RWHTS RbS`Y RcM^N R\\I\\S[Z',
	    'Po]GkG RRI[I R[MlM R[RlR RWCW[ R`HaL RdCdF RWK[P RbWk[ RhHgL RWJRU RbN^ViY RWHTS RhRgVdY\\[',
	    'Po]FkF RRI[I R^WeW RWCW[ R^Q^X R^QeQeV RWIZO RWGST RdDdHaK]L RdHjL R\\NkN RjNj[f[',
	    'Po`IiI RRJ[J R`MjM RaWhW Rh[l[ RWCW[ RaQaX ReFeP RaQhQiV R]ElEl[ RWMZP RWHST R]E]R\\Y',
	    'Po^HiH RRI[I R^LiL R^QiQ RZUlU RWCW[ R^D^U R^DiDiU RgWk[ RVK[P R_W[[ RWHST',
	    'PoRI[I R]QiQ Rf[j[ RWCW[ RcJcZ RWM[Q RbQgV RcQ`T\\X R_K_O RgKfO RWKRU R`C]K RWHTS R_HlHlTj[',
	    'PoRFlF RWJ^J R`JkJ R`OlO RRTlT R_C_G R_R_[ RaK`P RgHgS RULYM RYHSM RaVkZ R]LYQSS R]VSZ',
	    'Po\\GlG RSI\\I R[TlT RXCX[ RdCdG RdQd[ RWK[N R_KcO RgLlP RXIRU R`I\\Q RhIeP RWGVP',
	    'Po\\GlG RSI[I R^QjQ R`[d[ RWCW[ R^K^Q RdCdG RdQd[ R^KjKjQ RWN[Q RjVkY R_U[Y RWJRU RWHTT',
	    'PoSJ[J R]SkS R[ZlZ RWCW[ R\\G\\J RdCdG RdSdZ R\\GlGlJ RgGgNlMlO RWMZP RWKRU R_N\\O RWHTT RaG`N',
	    'Po\\GlG RRI[I RaNjN RaRjR RaVjV R]YlY RWCW[ R]L][ RaJaV ReCeJ RaJjJjV RYNZQ RWKRU RWHTT',
	    'Po^JlJ RRI[I R^OkO R^TkT R^YlY RWCW[ R_H_[ ReHeY RWM[Q R_H[L RWJRU RaC^J RgDeI RWHTS',
	    'Po\\ElE RRI[I R\\KaK RfSkS R[[l[ RWCW[ RaEaK R\\K\\SaSaZ RfSfZ RfEfKkKkS RWMZP RWKRU RWHTT',
	    'PoRI[I R]JkJ R]OlO R_ZkZ RWCW[ R_S_[ ReJeS R_SkSk[ R]EkEkJ RWIYN RWGSS R]E]Q\\Y',
	    'PoRI[I R]KjK R]PjP R[UlU RWCW[ R]F]P RfQf[ R]FjFjP RWIYN RbO^T RWJRT RdKaR RbCbF RWGTS RdGcP',
	    'PodFkF R^GbG RRJYJ RZMcM R[[^[ RVCV[ R_C_[ RgVb[ RVKYQ RfQjZ RiOeY RbQcW ReLiX RVIRV R[QZW RkGhU ReGgU RVFUQ',
	    'Po\\EkE RRI[I RWCW[ RfRfY RWM[P RdE`JcM^P`QbV RkJfNjV RVLRU R`J[L R`P[R RbLeR_VZX Rd[`[ ReHhT RWHTS RdNfW ReFgR',
	    'PoSI\\I R^UlU R^ZlZ RXCX[ R^P^[ RePeZ R^PlPl[ RXM\\Q R^I`N RcIfL RhIlL RjEhI ReEcI RWJRU R`C^H RWHTS',
	    'PoRJYJ RdJlJ RVCV[ R\\MaMaY RhCh[ RVKYQ RaZ]Z R`DbL RhIlV RVIRV R]D[M RgHcW RhGjT RVGTT R]M\\X',
	    'Po]EcE RRI[I R]LkL R]QlQ Re[i[ RWCW[ R]HkHkL RWMZP RbW^[ RWHTT ReNdQaY RbUjTi[ R]E]Q[X RiD`E',
	    'PoSI[I R]LkL RdSjS Rd[l[ RWCW[ R]G]J RdCdG RdLdZ R]GkGkJ RWMZP RWKRU RWHTT R^Q\\Z R^S`WbZi[',
	    'PoSG]G R_GkG R[[l[ RYCYN ReCeN R^N`P RXG[I]K ReGhIkL RWXSZ RYGRM ReG_M RTQhPeSYY RZZb[',
	    'Po^IkI RRI\\I R^MkM R]UlU R]ZlZ RXCX[ R]Q][ R^D^M R^DkDkM R]QlQl[ RXM\\Q RXIRU RWGVP',
	    'Po]IcI RfIlI RRJ[J R]McM RfMlM Rh[l[ RWCW[ R]E][ R]EcEcM RfEfM RfElEl[ RZPZQ RWHST',
	    'Po[GlG RRI[I Re[l[ RWCW[ R`N`[ R`C`J ReKe[ RgCgJ RlWk[ RWKZO R_O[S RfSbT RkMfR RWKRU R`K^Q RWHTT',
	    'PoSI[I R^JiJ R]TjT RWCW[ R]N]T R]NjNjT RWMZP RcJcTeWhYl[ RdChHlI RcC^H[J RcU`Y[[ RWHTT',
	    'PoSG\\G RRJ]J ReQkQ RRTlT R_R_[ R_D_L RfLfQ R_DiDiL RXDXL[O RWLSQ R`TcWk[ R]T[WR[ RbO\\Q RdGcN',
	    'PoRG]G R_IlI RRSlS RXCXP R_Q_[ RgIgP RWG\\L RVJRN R_RbVlZ RXFUK R_R[VRZ R_E_J_O RjD_E',
	    'Po]FjF RRIZI R[TlT RWCW[ RdCdJ RhXl[ ReWiX R_MbO RVIYP R\\P`Q RdU`Y[[ R\\JkJjN RVISU ReLePdV',
	    'PoRE_E RbJlJ RSJ_J RRSlS RZHZQ R_Q_[ RhJhQ RVFSJ R`ScVlZ RWCUH R]S[VRZ RbEbKaP RjDaE R^MXNRN R[MUN',
	    'Po[EeE RRIZI R[MeM R\\RdR Rh[l[ RWCW[ R`N`Y RgGgU RlDl[ RWKYP RbHeM RWHST R_E]L ReX[Z',
	    'Po_IiI RSI[I R`OiO R_VjV R]YlY RWCW[ R]D][ RdIdU R]DlDl[ RWM[Q RgQiS RWKRU RWHTT',
	    'PoRIZI R]NjN RZTlT RWCW[ R]H]T RaCaT RfCfT R]HjHjT RgVl[ RWMZP R`V[[ RWHST',
	    'PoSI[I R^KlK R`PkP RXCX[ R^D^K RgDgK R^DlDlK RaSeT RaO]S RWJZM[P RWJRU RcDbL`Q RjQeW][ RWGTR',
	    'Po]HdH RRJZJ R[OdO RfXlX RWCW[ RfFfZ RfFlFlZ R`H`SdW RWKYP RWHSU R]C\\M R`M_W',
	    'Po^IiI RSJZJ R[MkM Rd[h[ RWCW[ RfMlS RhEkH R`MZS RaV][ R^E\\I RYMZP ReIhO RbG^P RWHST RcD`N RcPaX R]SiSh[',
	    'PoRJ[J R`JhJ R\\VbV RgWjW RWCW[ R\\N\\X R\\NbNbV ReNe[ ReNkNkW RWM[Q RcCkK RcC[K RWKRU RWHTT',
	    'Po]FlF RRJ[J RcMlM Rf[j[ RWCW[ RaLa[ RdCdG RjIj[ RXNZQ RdQfV RbI^R RWHST R]F\\R[[',
	    'Po\\FlF RSIZI R^NlN R_SjS RWCW[ R_JjJjS RfVk[ RdCdF RcV^[ RXLYP RdHdSgX RVHSU R\\F\\R[Z RdMcV',
	    'PoSI[I R]JlJ R\\ZlZ RWCW[ RbJbZ RfJfZ RWM[Q R`DaH RWKRU RiCgH RWHTT R^N_W RjNhW',
	    'PoRI[I R]MkM R]RkR R[VlV RWCW[ R]I]R RdId[ R]IkIkR RWIYN R_D`G RgFdJ RhCfH RWJRT RWGTS',
	    'PoRI[I R_PiP R_UiU R[ZlZ RWCW[ R\\G\\J R_K_Z R_KiKiZ R\\GkGkJ RWIYMZP RbDeF RWJRU RWGTS',
	    'PobGlG RRJZJ RePkP ReUkU RVCV[ R\\D\\[ ReKe[ RVJYP RgEcN RVIRV R\\EaE_LaOaT^T RdMkLk[h[',
	    'PocHjH RRIZI R[LlL RWCW[ RVKZN RgEjH RbK]P RbC]H ReLgOlQ RdP]S RfS^W RiV_[ RVHST RdH]H RgG`H',
	    'PoRIZI R]JkJ R\\MlM Ri[k[ RWCW[ R]Q][ R]E]J RbQb[ RdMcQ RdCdJ RfQf[ RkEkJ R]QkQk[ RWJZQ RWGST',
	    'PoRI[I R\\JbJ R\\PbP RWCW[ R\\D\\V R[DcDb[ RfDf[ RXNZR RWHST RfElEiMlRlVhV RbUZV',
	    'Po_GlG RRI[I R^MjM R^PjP RWCW[ R^I^P R^IjIjP RbQ\\W R`TcWk[ RWKRU R`C]J RWKZOZQ RWHTS R`SiSfWaY[[',
	    'PoSIZI R\\JlJ R^QjQ R\\SkS R]XlX RWCW[ R]U]W R^L^Q RdQd[ R^LjLjQ RWIYMZP RWJRT R^FiFiJ RWGTS RbCaJ',
	    'Po]IjI RRI[I R]NjN R[QlQ RXCX[ R]D]M R^Q^Z RdDdM R]DjDjM ReUjZ RWJ[N RcQfX RWJRU RkSeV RWHTS ReY[Z',
	    'PoRIYI R\\JaJ RiMlM R\\PaP RVCV[ R\\E\\[ RdDiDjM RlJlM RfVk[ RVIYP RdQgX RcPkPhVeYa[ RaD\\E RVISU RbUYV RdFcM',
	    'PoRIZI R\\LcL ReLkL R\\PbP R\\UbU RWCW[ R\\E\\[ R\\EcEcL ReEkEkL RfVl[ RVLZP ReQhY RdPkPiVeYb[ RWHSU',
	    'PoRI[I R^LjL RfPlP RWCW[ R^D^L RgLgP RkDkL RlNlQ RWKZN RbP]V RgDaJ R`TdXk[ RaFgH RWKRU RaL`P\\Q RWHTT R`SiSeWbY[[',
	    'Po\\HlH RRJZJ R_KdK R_UdU RWCW[ R_O_W R_OdOdU RXNZR RhDkE RgUb[ RjMfW RlWk[ RWHST RgCgOgQhZ R\\H\\T[Z',
	    'PocHlH RZHbH RRIZI Rd[l[ RWCW[ R\\MaMaY ReLe[ RhIhV RlXl[ RWMYP RkLbP RWHSU RdCbK RkLkUiU R`[\\[ RWFTR R^C]H\\QZY',
	    'Po\\GlG RRJ[J R`JiJ R`MiM R\\QlQ R^ZlZ RWCW[ R^Q^[ R`C`Q RiChQgVkUkV RWM[P RWJRU RWHTS RbQaV_W',
	    'PoRIZI RbLjL R`PkP RWCW[ R^H^[ RbEbL RfLf[ RbEjEjL RXNZR RiUlX RdU`Y RfOiW RfObW R`C\\L RWHSU',
	    'Po\\ElE RRI[I R`KjK R`NjN R^QlQ R\\YlY RWCW[ R\\E\\[ R`H`N R`HjHjN RWM[Q RUORU RcNaTjW RWHTT RiQfV_X',
	    'PogElE RRJYJ Rf[i[ RVCV[ R^K^[ RfLkLi[ RaKdM RbDeF R_D[H RVJYP R_H[Q RVIRV RdSb[ RVFUQ',
	    'Po^JgJ RRI[I R[LlL RWCW[ RVLZP RgTlY RjQfR RkOiR ReOiW RbN[Q RbR[U RaOdSaW[Z ReLgU RWHST R_C^J R_FhFgL Rd[a[ ReReY',
	    'Po\\HlH RRIYI R\\PbP R\\TbT R`[b[ Rh[k[ RVCV[ R\\L\\[ R\\LbLc[ RfLfV RkKk[ RVJYQ R_C`F RhDfH RVHRU',
	    'Po\\FkF R]IkI RRJ[J R[MlM R_UhU R_ZhZ RWCW[ R_P_[ R^QiQi[ RWMZP RgOlS R`N[T ReIhQ RbI^Q RWHTT RbF`O RbDaM',
	    'PoRI[I R]MlM RaWiW R^WfW Rh[l[ RWCW[ R\\Q\\[ R]H]M R]EdEdV R]HlHlM R\\QlQl[ RWJZP RhSjX RWHST RkCbD',
	    'PoRIZI R^ZiZ RVCV[ R[F[J R^S^[ R^TiTi[ R[FkFkJ RVIXN RbCcF RaI[N R_KdPlT R`KhKcPZT RVHST',
	    'Po\\GlG RRI[I R^SiS R^WiW R[[l[ RWCW[ R^O^Z RcCcN R^OiOiZ RWM[Q RcFiLlM RcF]M RWKRU RWHTS',
	    'PoRI[I R]IkI R]LlL R_RkR R_VkV R][l[ RWCW[ ReSeZ R]DkDkI RgNkR RdL_R RVIXMZP RWHST R]D]S[Z',
	    'PoRIZI R\\KkK R]UlU Ri[l[ RWCW[ RbObZ RgOgZ R\\FkFkK R\\OlOl[ RVJYP RcCdF RWHST R\\F\\R[Z',
	    'Po]ElE RSI\\I R_MjM R]UlU R]ZlZ RXCX[ R]P][ R_H_M RdPdZ R_HjHjM R]PlPl[ RWLZO RXIRU RWGVP',
	    'Po\\ElE RRI[I R]NkN R[RlR RWCW[ R]H]N RaEaN RfEfN R]HkHkN RWK[O RWKRU RaO^WkZ RWHTT RhRfVdY[[',
	    'PoRI[I RaWhW Rh[l[ RWCW[ R\\N\\[ R_D_N RaRaW R_IeIeN RaRhRhW R_DjDjN R\\NlNl[ RXLZP RWJRT RWHTS',
	    'PodHiH RSI[I RcMlM R^SjS R^WjW Rf[j[ RWCW[ R^O^[ RdDdL R^OjOj[ R\\ElEkG RWM[Q RWJRU R_G\\M RWHTS',
	    'Po\\GlG RSI\\I R\\NlN R`ZkZ RXCX[ R`Q`[ R`C`J RhChJ R_SkSk[ RXL[P R`Q[U RWJRU RcJ_S RWHTS',
	    'Po^HjH RRI[I R^LjL R\\OlO Rf[j[ RWCW[ R^D^L R^DjDjL RWM[P RbT\\Z ReWa[ RaO[U RgRdY RWJRU RWHTS R^SkSj[',
	    'Po[GeG RSJZJ R\\QdQ Rh[l[ RWCW[ R\\K\\Q R`C`[ R\\KdKdQ RgFgT RlCl[ RaTdW R_RcV R^TYY RVJYQ R`P]V RWHSU',
	    'Po\\FlF RRI[I R\\LlL RWCW[ RVIYMZP R`HaK RdCdE RgHeL RgN`R RiR`V RWHSS RjVeY^[ R\\L\\S[Y',
	    'PoSGlG RTZYZ R\\ZbZ ReZjZ RTQT[ RTQYQZ[ R\\Q\\[ R_C_O R\\QbQb[ ReQe[ ReQjQj[ R_FcJlN R^GZJRN',
	    'PoRIZI R]IkI R]PjP R]TjT R[WlW RWCW[ R\\E\\H R]L]S RdGd[ R]LjLjS R\\DkEkH RWIYN RWJRT RWGTR',
	    'Po[GkG RRIZI R^MiM R[QkQ R]ViV RZ[l[ RVCV[ RcJcZ ReGkL RaG[M RVIYP RbC_J RVHRU RVFTS',
	    'PoRI[I R\\IkI R\\NkN RWCW[ R\\D\\N RcDcN RgQg[ R\\DkDkN RWM[Q ReNjS R_W[[ RbN^RZT RWHTT R_R_X',
	    'PoRIZI R^NhN R\\SkS RWCW[ RbSj[ RfIlN RhEfG RkHgJ R\\H`J[N RVJYP RdDhL R\\EbE_K RbU^Y[[ RVHSU RcNbV',
	    'PoRI[I R]JkJ R]OkO R_ZhZ RWCW[ R]E]O R_R_Z RdEdO R]EkEkO RbQeU RhWhZ RYLZP RiSlY RWJRU RWGTS R[SZY',
	    'PoRIZI R^SfS R]YdY RWCW[ R^L^S RbHbY R^LfLfS RVKYP RfVh[ RlWl[ RWHSU R\\EiEiVj[ R\\E\\SZZ RfH^H RgX`Y',
	    'Po[FbF R\\IbI RRJZJ R[TlT RWCW[ R_C_P RdEkEkN RXNZR RdUgYl[ RdObP RgPjOgO RcRaW_YZ[ RWHSU RgEeM RbL[M',
	    'Po\\FlF RaIkI RRJ[J R_MlM R_SkS R_WkW Rg[j[ RXCX[ R_P_[ R_PkPk[ RXM\\Q R`H\\M RWJRU RbC^K ReIfM RWHTS',
	    'PoRIZI R\\JcJ R\\OcO RiVlV RWCW[ R\\E\\Y R\\EcEcO RgEg[ RgElElV RVIYO RaRdY RcV\\Y RVHSU',
	    'PoRI[I R`JlJ RbMjM RaQkQ R`UlU Rb[l[ RWCW[ RfCfX RbFjFjM RWLZP R_S[Z RWHSU RZF^F\\N_N]W R[R]W`Zh[',
	    'PoWPZP RRSlS RZIZP R_P_[ RcJgLkP RRJ_J]L R_RcVlZ RdCaJ RYIVMRN RcGkGhJeMaO R_R[VRZ RTE^EYG RVG\\H',
	    'PocIkI RRJZJ R[[_[ RWCW[ R_L_[ RgCg[ R]H`K RZEcE^I RVKYR R_LZW RZMcMaQ RgGkU RWISU RgHcT',
	    'PoRF^F R_FlF R_ThT R^[l[ RXCXM R_O_Z ReCeM ReFkL RWWR[ RSOkOiR RXFRL ReF_L RWFYH]J RXSUZ RXTYW\\Zd[',
	    'Po]GlG RRI]I R\\MlM R^ZjZ RXCX[ R^S^[ RaCaI RdIdS RhChI R^SjSk[ RXM\\R RXIRV',
	    'PoRIZI R\\JdJ RfKlK RfRlR R\\WcW Rh[l[ RWCW[ R\\P\\Y R`D`P R\\PcPcW RfDlDl[ RXNZR RWKYP RWHST RfDfReY',
	    'Po\\GlG RSI[I R]RkR RWCW[ R]J]R RdCd[ R]JkJkR ReSlZ RcS\\Y RWIYMZP R`LaP RgLfP RWHTS',
	    'PoRI[I R]KkK R\\OlO Rd[h[ RWCW[ R]E]K RbEbK RdKdO RfEfK R]EkEkK RWMZP R`V\\[ RWKRU RWHTT RaTjSi[ RaO`W',
	    'Po\\FlF RSI[I R^MiM R^SiS R`[d[ RWCW[ R\\P\\S R^I^M RdSd[ R^IiIiM R\\PlPlS RYLZO RcCdF RWJRT RWGTS',
	    'Po\\GlG RRI[I R`QiQ R_UiU Rh[k[ RWCW[ R]K][ RdCdK RdQdZ R]KkKl[ RWM[P RaMbP RhMgP RWKRU RWHTS',
	    'PoRIZI R_KhK R_[b[ RWCW[ R\\N\\[ R\\NbNb[ RYNZR R]EdDjK RcE\\K RdTg[ RhUk[ R^QaR R^UaV RgNdT RjNhT RWHST',
	    'Po^IiI RRI[I RfRkR R]UkU R]YkY RWCW[ R]M][ RaIaQ RfIfR R]MkMk[ RWM[Q R^F\\J RbEhDjJlJ RWHST',
	    'PoRI[I R^KlK RaRlR RaVlV RaZlZ RWCW[ RaNa[ ReEeJ RaNlNl[ R^ElElJ RWKZN RWGTS R^E^Q\\Y',
	    'PoSJZJ ReQiQ R`[g[ RWCW[ R`U`[ RhXh[ RbSeV RVLYO RjVlZ ReL`Q RaJ\\O R_C]I R\\U[Z RgFcO RbF_KgM RWIST R^GkGjQ',
	    'PoRJ[J R]KkK R[OlO R`RjR RWCW[ RcFcJ RWMZP R^VZ[ RjFlJ R`QbUeXl[ R^E[J R`Q]Y RiTeX_[ RWHST RaK_U RdD]E RiC`D',
	    'PoSI[I R^JiJ R^RiR R^ViV R[ZlZ RWCW[ R\\F\\I R^N^V RdCcF R^NiNiV R\\FkFkI RYNZQ RWJRU RWHTS',
	    'PoeFlF R\\FcF RRI[I R\\SlS RWCW[ R^L^S R`C`I RhChI R^LjLjS ReTl[ RWM[Q RWHST RcU^Z\\[ RdIdQcV',
	    'PocGlG RRI[I R^NjN R^RjR R^VjV RWCW[ R^K^V RcCcJ R^KjKjV RWK[N RfXl[ RWJRT RaX[[ RWGTR',
	    'Po`ElE RRJ[J RaMkM RaRkR RWCW[ R^G^[ RaIaR RaIkIkR RXNZR ReWa[ RaTeXk[ R^EZN RWHST RfEfReX',
	    'Po[GlG RRI[I RcLhL R^OlO R[SlS RWCW[ R^D^O RcCcL RdOd[ RiDiL RdSjY RYNZQ RdS^XZZ RWHST',
	    'PoSI[I R^IjI R_PjP R_TjT RaXlX R]XjX R[XgX RWCW[ R^D^I R_L_X R\\LkLj[ R^DjDjI RWIYN RWJRT RWGTS',
	    'Po^HkH RSI\\I R^LkL R^XhX Re[j[ RXCX[ R^Q^Z R^D^L R^DkDkL RXL[P RaMZT RcS`W RcTgV RXIRU RXGVP R^QlPj[',
	    'PoSIkI RTMjM RUQiQ RSTkT R\\C\\H R_M_[ RbCbH RZJ[M RVDWG RhDgG RdIcL RaVkZ R]VSZ',
	    'Po]GgG RRI[I R\\LlL R`UjU R`ZjZ RWCW[ R`P`[ RcCcL R^QjQj[ RWK[P RgMiO RWJRU RjDiGbNaPZR RWHTS',
	    'PoRI\\I R]JlJ R`RkR R`VkV R`ZkZ RXCX[ R`N`[ RfEfN R`NkNk[ RXM[Q RWJRU RWHTS R]E]R\\Y RjD^E',
	    'PoSI[I R]LaL RfLkL R]QaQ RfQkQ RWCW[ R]G]S RfFkFkR ReUjZ RWK[O RbU]Z RdCdRgX RUPRU RcQ`X RaE\\G RWHTT RcMcU',
	    'Po\\FkF RSI[I R]IjI R[MlM R\\UjU RWCW[ RfMkR R`M[S RVIYNZQ ReIgO RcTdWfYk[ RWKRU RbH^P RbV_Y[[ RWHTS RcC`N RcQaX RfP_Q',
	    'Po\\HkH RRIZI R^LdL RWCW[ RWMZP RhDjF R^UaVcW^Z RgVb[ RjOfX RkLhV RWHST R`L^U R\\OdObW RlWl[ RfCfMhY R\\H\\RZY',
	    'Po\\GlG RSI[I R_KhK R_OhO R[SlS RWCW[ R_C_O RdOd[ RhChO RdSjY RWM[Q RWJRU RWHTS RdR]Z[Z',
	    'Po\\FlF RRI[I RgYkY RWCW[ R\\L\\O R^Q^X R`HaK RdCdF RdNd[ R^QkQkX R\\LlLlO RWM[Q RhGhK RWKRU RWHTS',
	    'Po[EkE RRJZJ R\\T`T RZZlZ RWCW[ R\\L\\U R\\L`L`T R_JdJdW RXNZR RhSlW RiSeW RfNiU RjMgU ReKkKhS RWHSU R`E_J RcX_X',
	    'PoWRgR RRUlU RWNWR R_R_[ RWNgNgR R]FkFhH R_D[H RTDYF R_UbVjZ RbI[L R]VSZ RbIkL RYJRJ',
	    'Po^HcH RRJ[J ReMlM R^VjV R^ZjZ RWCW[ R^C^M R^Q^[ RfCfM R^QjQj[ RlKlN RWM[Q RcNbQ RWJRU RkFeH RWHTS RcL\\M',
	    'PoSIYI RZLbL RVCV[ R_E_[ R`NaQ RVIYP RfSc[ R_LZW RgCgQjZ RkIjP RgNdY RbD[E RVFSS R^I]Q RdIcO RgJiX RgKeW',
	    'PoRJ[J R_JfJ RZZlZ RWCW[ R]S]Z RaSaZ RfSfZ R]SjSjZ RYNZR ReLaP R`LeN RWHST RjPgP R\\EfEfHkHjQ R_E^M',
	    'Po^EjE RRJ[J R[SlS RWCW[ RaPdPc[ ReJeP RcSjZ RWMZP R_XZZ R\\JkJhM RcS]Y RaGgJ RiEcH RcJ`M\\O RWHTT',
	    'PoSI\\I R^JjJ R^NjN R]RkR R]VkV R\\[l[ RXCX[ R^F^N RdRdZ R^FjFjN RWL[P RcCcF RXIRU RXGVP',
	    'Po]IlI RRI[I R\\SlS RWCW[ RdCdO R^D`G RiDhH RdH^N RYNZQ RdHhMlN RWKRU RbP_Wk[ RWHTT RiTeXcZ\\[',
	    'PoZKdK RZOdO RRSlS RZFZO R_O_[ RZFdFdO RTEXI RjEfI RfLlO R`SdWlZ R^C_F RXLRO R^SZWRZ',
	    'PoRI[I R^JjJ R^OjO R`[d[ RWCW[ R^F^O RdOd[ R^FjFjO RfTkY RWM[P RcOgV R`U\\Z RjQgU RWJRU RcCcF R\\RaR_W RWHTT',
	    'PoSI[I R\\JlJ R^UiU R^ZiZ RXCX[ R^Q^[ RdEdP R^QiQi[ RXMZO RdIkP RcI\\Q RVMRU RWHTS RiD]F',
	    'PoRJ[J R_LiL R\\YbY ReYlY RWCW[ R\\P\\[ R_E_L R\\PbPb[ RePe[ R_EiEjL RePlPl[ RWJZP RWJRU RWHTS',
	    'PoRJ[J R]KkK R\\QkQ R]UjU R[ZlZ RWCW[ R\\F\\I RdCdG RdRdZ R\\FlFlI RXNZP RgMkQ RWKRU RbK_P RWHTT',
	    'PocElE RRIZI R\\JaJ R\\OaO RcOlO Rh[l[ RWCW[ R\\E\\X R\\EbEbO ReEeO RiMi[ RlWl[ RVLZP RfU`[ RiEiOeX RWHSU R`RbWYX RiIgT',
	    'PoRG]G R_GlG RVUhU RVZhZ RVQV[ RXCXO R_Q_Z ReCeO RVQhQh[ ReGkM RWIRM ReGcI_M RXI]K RXEVK',
	    'PoeGlG RRIZI RWCW[ R\\D\\K R]O]V R`C`L R]OiOiV RdVd[lZl[ RfJjK RVIYQ RfDcJ RVISU R`YZ[ RcQbX',
	    'PoRI[I R]IiI R]NiN RZ[l[ RWCW[ R\\R\\Z R]E]N RaRaZ ReReZ R]EiEiN R\\RjRjZ RWIYMZP RWJRU RWGTS',
	    'PoRI[I R\\RbR R\\VbV R_[b[ Rg[j[ RWCW[ R\\N\\[ R\\NbNb[ ReOeW RjNj[ RWMZQ RdCjJ R_KhJlJ RcC\\J RUPRU RWHTT',
	    'Po\\FlF RSI[I R\\MlM R\\RlR RWCW[ RgTlY RcCdF R_U[Y RWIYN R_HaK RWJRT RhHfM RWGTS RdMd[`[',
	    'PoSIZI R`NhN R_WhW Rh[l[ RWCW[ R\\I\\[ R_Q_X R`J`N R`JhJhN R_QhQhW RaDlDl[ RVKZO R\\D_G RWHSU RWEUQ',
	    'Po\\IkI RRIZI R]PiP R[UkU RWCW[ RcLc[ R]D^H ReIlO RbCcG RiDhGeI R`K[O RVIYO RWHST',
	    'Po[EbE RRIYI R[JbJ RdNlN RdTlT RVCV[ R\\N\\V R_J_[ RbNbV RdHdU RhCh[ RdHlHlU RVMZQ RVHRV',
	    'Po^HjH RSI[I R]LjL R[UbU ReUlU R[ZbZ RdZlZ RWCW[ R[PbPb[ R]DjDjL RePlPl[ RWIZO RWJRT RWGTR',
	    'PoRIZI R[OgO RVCV[ RbGb[ RkXk[ RhXj[ RVIYP RiMkN RjQgSlU RjFhHlJ R_IZJ RVHRU R\\EgEhM RgPhY R^I^Q\\W',
	    'Po]IkI RRIZI R`SjS R`WjW Rg[j[ RWCW[ R`P`[ ReKeO R`PjPj[ R]DkDkI RXNZR RiKlN RbL^O RWHSU R]D]Q[Y',
	    'Po\\DkD RRI[I R^KjK R^MjM R^QjQ RWCW[ R^H^P RdDcH R^HjHjP RWM[P RbQ\\V RWJRU R`TcXfZl[ RWHTS R`TiTfWbY[[',
	    'PoRIZI R\\JkJ RZZlZ RWCW[ R]R]Z RaRaZ ReReZ R]RjRjZ RfLkP RaL\\Q RhDgG RVIYP R_DaH RWHST',
	    'PoRI[I R]IkI R^NkN R\\SlS RWCW[ RaRa[ RbKbR RhKhR R]DkDkI RfVk[ RVKZN RjUgW ReRhY RWGST ReY_Z R]D]Q[X',
	    'Po\\GlG RRJ[J R^MkM R^PkP R[TlT Rd[h[ RWCW[ R^J^R RdCdR RhRh[ R^JkJkR RWMZP R_VaY RgDjE RWKRU RWHTT',
	    'PoRIZI R\\JbJ ReJlJ R][a[ RWCW[ R\\EbEbJ ReElElJ RVLZP R^QZS R_UZW RiQdS RiUdW RVHSU Rk[g[ ReJeOlNlUk[ R\\J\\ObObUaZ',
	    'PoRI[I RaPhP R\\[l[ RWCW[ R^S^Z RbSbZ RfSfZ RhMhP R^SjSjZ RWM[Q RiFaN RjIlM RaDfF R^I\\M RbGbO\\P RWHST',
	    'PoRIZI R\\KcK R\\PcP RVCV[ R\\G\\Y R_C_G R\\GcGcP RfEf[ RVMZQ RcSaUcZ RVIRV RaWYY RfElEiMlSlWhX RVGST',
	    'PoRJ[J R_ZiZ RWCW[ R]F]J R_S_[ RdCdG R_SjSj[ R]FkFkJ RWMZP RdLkS RgJkN RdM\\T RaJ\\N RWHTT',
	    'PoRF_F RSI^I RiLlL RUO\\O RRR^R RSLRO RXCXI RXOX[ RRL^L^O RbEiEiL RlJlL R[U]X RYRRY RbQdUhYl[ RXT\\U R`PjPiTdX^[ RbFaL',
	    'Po\\ElE RSI[I R[NlN R]VjV R]ZjZ RWCW[ R]R][ RaEaN RfEfN R]RjRj[ RVIZP R]G_K RjGhL RWHST',
	    'Po_ElE RTG]G RRJ^J R`LjL R_OlO RRSlS R_Q_[ R`E`O R`HjHjL RXFXL\\O RXLTP R_RbVlZ RUCRI R_R[VRZ',
	    'Po\\GlG RSIZI R`OhO R_ZiZ RWCW[ R_S_[ R`C`I RgCgI R_SiSi[ RVIYO RdJgMlO RWIRS RcJ_N[P',
	    'Po]HkH RRI[I R]MjM R\\SlS RWCW[ RaVa[ RdHdS RWM[Q R`D`H RjVl[ R]V[[ RhCfG ReVf[ RWHST',
	    'Po\\FkF R]IkI RRI[I R[MlM R\\TjT RWCW[ RdQd[ RWKZN RgNlS R`NZT ReIhQ RcTgXkY RVMRU RbH^Q RcT_X[Z RWHTT RbDaM RfP_Q',
	    'Po]FlF RRJ[J R]PlP Rd[i[ RWCW[ R]L\\N RdCdF RdMdP R\\LlLlN RWM[Q RaW][ R`G`K RhGhK RWKRU RWHTS RbP`X RaUjTi[',
	    'PoRJXJ RjMlM RZPbP RVCV[ ReDjDjM RgVl[ R`SbW R]FaJ RiUc[ RaE[J RVKXP RdPhX RdOkOgW R[SYW RVIRU R^K^[\\[ RVFTR ReEdM',
	    'Po]DlD RRI[I R_MkM R_QkQ Rb[e[ RWCW[ R_I_Q ReQe[ R_IkIkQ RWM[Q RjTlY RVMRU R`T^Y RWHTT ReEdI R]D]Q[Y',
	    'PoRI[I R]KkK R]QlQ R^UlU RWCW[ R\\F\\J RdCdG RdHdK RdQd[ R\\FlFlJ RWM[P RaMaQ RhLgQ RWJRU RWHTS',
	    'PoRI[I R^MjM R^PjP R[TlT RWCW[ R\\E\\H R^I^P RdQdT R^IjIjP R\\EkEkH RgWl[ R`W[[ RYMZQ RWKRU RWHTT',
	    'Po\\FkF R]IjI RSI[I R[LlL R[UkU RWCW[ RcTc[ RhWkZ RXMZQ RhQlU RcCcL^P RfNaS R^W[Z R\\PbR RWHTS',
	    'PoSIZI R^KiK RWCW[ R\\G\\J RcDdG ReQeY R\\GkGkJ RWIYN RiUlX RgPjW RWJRT RcK`N\\O RdO`R\\T ReR`W\\X RWGTS Rd[a[ RiNdOeV',
	    'Po\\FbF RRIZI R^OkO R[SlS R]ZjZ RWCW[ R]U]Z RdNdZ RcGcK RjUj[ R_M\\P RXLZP R^G^K RjGhK RWHST RjD`E',
	    'PoeGlG RRI[I RcMlM R[SlS RWCW[ RcPc[ ReGeL RiDiP RcRjY RWM[Q RcR\\Y R]IaK\\P RUPSU R_D\\J R^GcG`N RWHTT',
	    'Po]HjH RRI[I R]LjL RZSlS Rf[l[ RWCW[ R]D]L RcLcS RfSf[ R]DjDjL RlWl[ RWM[P RiMhQ R]N_Q R_YZ[ RWHST R`S_Y',
	    'PoRG]G RSN\\N R_OkO RSR\\R RRV^V RSJSR RXCXJ RXRX[ RSJ\\J\\R ReJe[ RhTlX ReElK RbT]X RdE^K ReMiV ReMaV',
	    'Po\\DlD RRIZI R^JcJ RgJkJ RgNkN RgRlR R\\ZlZ RWCW[ R\\D\\[ RgGgX RXNZR RWHSU RcGcS^S R_NcNbV',
	    'PoSI[I R_PlP RcSkS RcWkW RWCW[ R\\E\\H RcOc[ RdCdF R\\ElFlH RWKZN R`O\\S RgEfLkKlL RWIRT RbF`L_Q R_K\\L RWGUO',
	    'PodEjE RRJ[J RbKhK R`KfK RcNjN R^RlR RWCW[ RcIcN RdCdH R\\HlHkK RdPeR RXMZP RaTeXk[ RWHST RiTfXbY][ R\\H\\R[Y',
	    'Po^ElE RRI[I R^IkI R^MkM Rf[j[ RWCW[ ReEeQ RVKZN RgTjV RdTeX R]T[Z RaUaY RWHSS R^E^RlRk[',
	    'Po^GkG RRJ[J R^JjJ R^NjN R^RkR R[UlU RWCW[ R^G^R ReFe[ RWM[Q R^G[J RWKRU R_C\\I RWHTT',
	    'Po]FkF RRIZI R\\JlJ R]QjQ RWCW[ R]M]Q R_U_[ RcCcJ R]MjMjQ ReUjZ RVJYO RdQgX ReQZW RlTfV RVHSU RdY]Z',
	    'PoRIZI R\\JbJ RdKlK R\\NbN Rf[j[ R^[b[ RWCW[ R\\F\\S R_C]FbFb[ RjCj[ RVLZP RbQ[Y RfNgT RWHSU RbRZS',
	    'PoTRkR RSMSP R_O_[ RSMkMkP RkEhG RUESG R]E[G RbE_G RXH]J R_RcVlZ ReHjJ RdI_K R_R[VRZ RWIRK RYCWI ReCdI',
	    'PocGkG RRIZI RbMlM R[N`N RbQlQ R[W`W Rf[i[ RWCW[ R[E[Y R[E`E`W RgCgL RiNi[ RdTeX RVIYP RVHSU',
	    'Po\\FgF R[IlI RRJZJ R`QkQ R]WjW R]ZjZ RWCW[ R]S][ R`K`Q RaCaI R]SjSj[ RWMZP RjEgH[N RWHTT RiL`M',
	    'Po\\GkG RRI[I R[MlM R^QiQ RWCW[ R^J^Q R`T`Z RdCdF R^JiJiQ ReUj[ RWM[Q RdQgX RVMRU RcQaSZV RjTfV RWHTT RfY]Z',
	    'PoRJ[J R^NjN RZZlZ RWCW[ R]R]Z R^E^N RaRaZ RfRfZ R^EjEjN R]RjRkZ RcIgL RYNZQ RWKRU RdFbK RWHTT',
	    'Po\\FlF RRJZJ RdPkP RcVlV Rd[l[ RWCW[ RhJhV RVKXNZQ RcCdF R`W\\[ RWISU R^JaJ^QbQ_X RkJcJ R^S_WbYiZ R\\F\\RZZ',
	    'PoRI[I RhNkN R]UkU R]ZkZ RWCW[ R]E]M R]Q][ RdQdZ R]QkQk[ RdElElM RWMZP RdNbN RWHTT RgEeM RaHcL[N RbD\\E',
	    'Po\\FbF ReFlF RRI[I R\\JlJ R_NiN R_SiS R[WlW RWCW[ R_J_W R`C`H RhChH RiJi[ RWM[Q RUPSU RWHTT',
	    'Po]GlG RRI[I R^NlN R_QjQ R_ZjZ RWCW[ R_T_[ ReHeT R_JjJjQ R_TjTj[ RcDfF RWIZP RWGSS R]G]R[Z',
	    'PoRI[I R_MkM R_QkQ R_UkU R_ZlZ RXCX[ R]G]J R`L`[ RfKfZ R]GkGkJ RWK[O R`K[P RXIRU RdC_N RfIeO RWGVP',
	    'PoaJkJ RSJZJ_K RaOkO RaTkT R^[l[ RVCV[ RaEaO RfEfZ RaEkEkO RVJYQ R]D^UYV RVIRU',
	    'Po\\FbF RdHkH RRIZI RcOhO Rh[l[ RWCW[ RdFdK RcThTi[ R`KkKkN RlXl[ RVIYO RWHST ReLeRjQkR RcTb[ R\\F\\SZZ R`K`S_Z RkDaE',
	    'Po^IjI R\\MlM R]SjS RdOd[ RdSkZ RVIXK RVOYR RgMlQ RhDlH RaM\\Q R_E\\H RdS]Z RfIhO RcG_O RcDaM RTEZEZ[W[ RTETSS[',
	    'Po^HkH RRJ[J R^LkL R_[c[ Rh[l[ RWCW[ R^D^L R\\OcOc[ R^DkDkL ReOlOl[ RWMZP R^W[Y R`U]X RjUfX R_R[S RiReS RWHTT',
	    'Po]FjF RRJ[J R\\KlK R\\PjP R[ZlZ RWCW[ R]S]Z RaSaZ RdCdJ RfSfZ R]SjSkZ RgMkQ RYNZQ RaK_P RWHTT',
	    'PoSI[I RcQjQ R]ReR R[UlU RWCW[ RgNkR RWIXMZP R]G^I R\\EbFcI RcTdWfYk[ RiFhI RfLaN RaJ\\NcN\\Q RcSbW`Y[[ RWGTS RjD`E',
	    'PodGjG RRJ[J R_RiR R_ViV Rf[i[ RWCW[ R\\K\\O R_D_K R_O_[ R_OjOj[ R_DjDjK R\\KlKlO RWM[Q RWJRU RWHTS RdHdL',
	    'Po]IkI RRJ[J R\\LlL R^TjT R[[l[ RWCW[ R]D]I R^O^T RdCdI R^OjOjT RkDkI RWM[Q RaVaY RWJRU RgVgY RWHTS',
	    'PoSEkE RXJfJ RYPeP RgRjR RRTlT RTLTR RYNYQ R_C_F R_R_[ RYNeNeQ RXGXIfGfJ RTLjLjR RaVkZ R]VR[',
	    'Po\\FlF RSI[I R_NiN RaWhW Rh[l[ RWCW[ R]P][ R_I_M RaSaY RdCdG RaShShW R_IiIiM R]PlPl[ RWM[Q RWJRU RWHTS',
	    'PoRJYJ RbQiQ RZ[][ Rc[f[ RVCV[ R^K^Z RfQf[ RVKXO RhJbQ RiTlY RiNkR RcTaZ R[E`E]L RfFdJ R`OYQ RVIRV RkDbE RiIgLbK',
	    'PoSK^K RZQ]Q RRTlT RYMYO RUF]F]Q R_R_[ RhIlHlI RXGYI RbLeOkR R`TcWk[ RXCXF RbEaI RaKjKgN`Q R]T[WR[ RbDhEhI RUFUKTP',
	    'PoSI[I R]TcT RfTlT R]ZlZ RWCW[ R^O^[ RfNlNl[ RWKZN R^H_L RdGdK RkGiL RWJRT RcN]O RWGTS RkD`F',
	    'Po\\ElE RSI[I R_LjL RfSlS R`ViV Rh[l[ RWCW[ R]O][ R_H_L RdVd[ R_HjHjL R]OlOl[ RWL[P RWJRU RcOaS^T RfOfS RWHTT',
	    'Po[FlF RRIZI R]SeS R]WeW ReZiZ RWCW[ R]O]Y R`C`H R]OeOeW RgCgH R^J\\N RVIYO RVHST R]LjLjUiZ',
	    'PoRI[I R`NjN R`QjQ R`TjT R\\VlV RWCW[ R\\L\\V R`K`T R`KjKjT RYNZR RgXl[ RaX[[ RWHST R_C^IkHkI RiD^F',
	    'Po]DkD R_GjG RRJ[J R]JlJ R^TlT Re[h[ RWCW[ R`J`P RhQh[ RaVcY RXNZR RjLfN RVKYP RdKgOlQ RWHSU ReO^P R]D\\Q[Y',
	    'Po_GlG RRIZI RbLkL R`RlR RWCW[ RhWl[ R^R[S ReV`[ RVIXMYP RfLfSiY ReCfG RfRcY R\\K]N RWHST RcIaO RfOeW R_G_R]Y',
	    'PoRG_G RbHjH RbMjM RTL^L RgQjQ RRTlT RTITN R^I^M R_Q_[ RbDjDjQ RTDVE RaM]Q R\\C[F RYGXNUQ R`TcWl[ R]T[WR[ RbDbJaN',
	    'Po\\FkF R]IkI RRI[I R\\LlL R]SjS R[WlW Rf[j[ RWCW[ R]P][ R`C`L RcLcV RgCgL R]PjPj[ RWMZP RWHTT',
	    'PoRIZI RbLjL RbVjV Ra[l[ RVCV[ RbGbX RfCdG RbGjGjL RbPjPkX R\\J^L R\\D_F RVNZP^O^XZ[ RVHSU',
	    'Po`IgI RSI[I R^OiO R^RiR R`ZjZ RWCW[ R^L_U`[ R^LiLiR R`UjUj[ RWMZP RdCkJ RdC]J RWKRU RWHTT R^O]Y',
	    'Po]GlG RRJ[J R]KkK R\\OlO R`TkT R]ZlZ RWCW[ RdGdO ReTeZ R^UZX RYNZQ R`C`F R`O\\W RhChF RWHTT',
	    'Po[IkI RRJZJ RZPlP R\\SjS RWCW[ RaEa[ RfEf[ R\\LjLjS RWMZP ReSlZ R`SZZ RWHSU RcD\\F RjDdF',
	    'PoRI[I R]KjK R]PjP RWCW[ R]G]P R]GjGkP RWM[Q R_W[[ ReWjUkW RdGdZlZl[ RcCbG RgRfV RWHTT R`Q^X',
	    'PoTJ]J RaJjJ RRO]O R_OlO RTETJ RXKX[ RTE]E]J RaEaJ ReKe[ RaEjEjJ RXQ\\U RhTlX RbS]X RVTRX RfOiV RXNUV RdOaU ReNcR',
	    'Po[FlF RRIYI R\\KdK R]VjV R]ZjZ RVCV[ R]R][ RcRcZ R]RjRj[ R\\OdOiMlP RcCdF RaF\\K RVJYQ RfI[O RVHRU RVETQ',
	    'Po^DjD R]IlI RRI[I R^OjO R^SjS R^VjV RWCW[ R^L^V RdDdI R^LjLjV RiYl[ RgXjY RWM[Q RWJRU RaX\\[ RWHTS',
	    'PoSI[I R]IjI R]MjM R[QlQ R^WiW RWCW[ R]F]M RcSc[ R]FjFjM RfQjU RbO\\U RcCaE RWIZO RUORU RbM`R RWGTS',
	    'PocFlF RSI[I R^MjM R^PjP R\\TlT RWCW[ R^J^P RcCcI RdPd[ R^JjJjP RdSjY RWM[Q RWJRU RdS^Y[Z RWHTS',
	    'PoSEkE RVHhH RVMhM RYRhR RRUlU RYNYR R_R_[ RWPhPhR RSJSKkJkM R_C_H RZNSQ R_UTZ RaVgYlZ',
	    'Po\\FlF RRI[I R`NjN RaXhX Rh[l[ RWCW[ R]Q][ R`H`N RaTaY RaThThX R_JjIjN R]QlQl[ RWM[Q RbF[L RVLRU RbC`I RWHTT',
	    'Po\\GkG RRI[I R]KjK R\\NlN R`[c[ RWCW[ RdGd[ ReTkZ R]Q`T RjQgT RYNZQ RdPgV R`D`G RhCgF RaV[Y RWHTT',
	    'Po\\FlF RRI[I R_MjM R_PjP R_SjS R[VlV RWCW[ R_I_S RdCdI R_IjIjS RiXl[ RWM[Q RWJRU R`Y[[ RWHTS',
	    'Po[DlD RRJYJ R\\MkM RVCV[ R\\H\\M R]O]W RaDaM RfDfM R]OjOjW R\\HkHkM RVJYQ ReXl[ RcV`Y\\[ RVHSS RcRbX',
	    'PoeHlH RRIYI RZZlZ RVCV[ R\\R\\Z R\\E\\N R`C`O RaRaZ ReReZ R\\RjRjZ RfKjN RVIYP RfDbL RVHRU RVFTS',
	    'Po]GlG RRIYI R]JkJ R]MkM R]PlP Rf[k[ RWCW[ R^F^Q RdGdP RcCeE R_W[[ RVIXMZP R^C\\I R[SgSfVlVk[ RVHST R`S_Y',
	    'Po]IjI RRI[I R]MjM R]PlP R[YgY Rf[j[ RWCW[ R[U[Z RaSaX RcCcF RgUgX R]FjFjM RYNZQ RWKRU RWHTT R]F]SkSj[',
	    'Po]FkF RRJ[JlK R]RkR R]VkV Rd[l[ RWCW[ R]N]X RdCdF RdNd[ R]NkNkV RlXl[ RWM[Q R`G`J RWJRU RhGgJ RWHTS',
	    'PoRIYI R^KeK R^OiO RZUlU RVCV[ R^K^U ReOeU R\\FkGkJ RcCdF RVIYP RfXl[ R`XZ[ RVHRU R\\G\\J RVFTS RhJ`J',
	    'PoaGiG RRIZI R`LlL RcUkU RcYkY RVCV[ RZL]L]Z RcOc[ ReCeL RbQkQk[ R\\E^H RjIaQ RVIYP RkEiJ RaX\\Y RVHST',
	    'Po^EhE RSIZI ReNkP R[TkT RWCW[ RcRc[ R\\LbL]Q RdLkLfP RVJYQ R\\MbP RcShXlZ RcS^XZZ R_FiI RWHST RhEbH]I',
	    'PoRI[I R^VkV R^ZkZ RWCW[ R^Q^[ R\\DcDcN R^QkQk[ ReDlDlN RWM[P RiFfH RjIgL RcOdQ R`J]M R_G\\H RWHST',
	    'PoRI[I R^JjJ R\\PlP R^XeX Re[i[ RWCW[ R\\F\\I R^S^Y RdDdF R^SeSeX RiPi[ R\\FlFlI RWMZP RcKjN RWKRU RdHcL]N RWHTT',
	    'Po`FlF RSIZI RaNkN RaQkQ R_UlU Ra[l[ RWCW[ RaJaQ RfCfY RaJkJkQ R\\E_H R[M^M^X[Z RWIYO RVLRU RWGTS',
	    'Po[FbF RdFlF RRIZI R\\IbI RdIkI RdMlM R[VlV R\\ZiZ RWCW[ R_C_O RhChO R]QiQi[ RWMZP RWHSU RbM[N',
	    'Po]FlF RRJ[J R^LlL R_NjN R_TjT R_WjW Rh[j[ RWCW[ R_Q_[ ReCe[ R_IjIjN R_QjQj[ RYNZQ RWHST R]F]Q[Y',
	    'PoRE^E RbJlJ RSJ^J RSM^M RRO^O RRTlT RSGSM RYCYQ RSG^G^M R_Q_[ RhJhQ R`TcWl[ R]T[WR[ RkDbEbJaN',
	    'Po[FdF RRIZI R\\McM R\\[_[ RWCW[ R\\I\\M R`S`[ R`C`G R\\IcIcM RfEf[ RXNZR R\\PcP_T RWHST RfElEiMlSlWiW RcU[V',
	    'PoaEkE RTH]H R_JlJ RTJ]J RTM]M RTO]O RRTlT RTFTQ RTF]F]J R_R_[ RaE`J RaTcVkZ RfJbO^P R]T[VRZ RfFfPlOlQ RfEdM',
	    'PocElE RRIZI R\\KbK RcOlO R\\ObO R\\SbS R\\VbV Rh[l[ RWCW[ R\\F\\[ R^C\\FbFbO ReEeO RiMi[ RlWl[ RVLZP RfU`[ RiEiOdX RWHSU RiIgT',
	    'Po\\FlF RRJ[J R]KkK R]NkN R[UlU Rd[h[ RWCW[ R]H]N RdCdQ RhRh[ R]HkHkN RWMZP R_VbY R\\RjQlS RWKRU RWHTT',
	    'PoZGbG RRJXJ R\\L`L RZPaP RVCV[ RUKXQ R_GaL R^DZL R]P[T`T_Z\\Z RfEgUiZcZ RUHSU RjDjMjPl[ RkDcEdQb[ RfPeZ',
	    'Po[GlG RRJYJ R`LdL R\\PeP R^ZaZ RVCV[ RaIaPaZ RhDjF RhVcZ RVKYQ RdSdV RjOfY R^S]X RlWj[k[ RkLhW RgCgOhZ RVGST R`MaT R[G[TZ[',
	    'Po[HjH RRJZJ RZN`N RWCW[ RVLZP RfFjI RcKhQlR RgJfO R\\J[N R`C^H RbK\\RYS RbR\\T ReT\\X RWHST RhWcY\\[ RiLkNeN',
	    'Po]IkI RRI[I R]OkO R]RkR R]VkV R]ZlZ RWCW[ R]D]I R^M^[ RdCdI ReMeZ RkDkI RWL[O R]OZR RWKRU R_K\\Q RWHTT ReKeM',
	    'PoSIZI RaOkO RaRjR R`VkV Ra[l[ RVCV[ RfNfY R\\XY[ RdD`I RVJYP R[E]I RbGfKkM RZN^N]XcZ RcFjFeK_M RVHRU',
	    'Po\\DlD RRH\\H RfMkM R]PkP R]TkT RWCWT R\\W\\[ R]H]T RfDfL R]HkHkT RWJ[N RhWl[ RbDbK_N RVWTZ RbWc[ RWGSQ',
	    'PoRIYI R\\OcO R\\TcT R`[c[ RVCV[ R`J`T R[JcJc[ RjCj[ ReMhO RfGhI RVKYQ R]FaF_L RVHSU RlSdT R^C[J\\TZ[',
	    'Po`FiF RTIYI RRLYL R\\LlL RcQgQ RRTlT RTDTH RYCYR R_R_[ RgJgQ R_MaP R]GbI RaVkZ RhGaJ R\\VR[ RcD\\G RULTR R`FfH\\K',
	    'Po\\EdE RRI[I R\\KkK R_PjP R^VkV R_[l[ RWCW[ R_S_Z R_M_P R_MjMjP R^SkSkV R\\GkGkK RWM[Q RdSdV RWHTT R\\E\\R[Z RjDbD',
	    'PoRI\\I R]JjJ R]QkQ RXCX[ R]M]R R]F]J RdCdS RaUa[hZh[ R]FjFjJ R]MkMkR RXM[Q RhVlY RbTeV RWJRV R]V[Z RWHTS',
	    'PoSI[I R^JjJ R_QcQ R\\ZlZ RWCW[ R\\E\\[ R_M_Q R_McMcQ RjTjX R\\ElEl[ RWL[P RhGjH RfGgRbW RiMfU RWHST RdS^T RfLgW',
	    'PoTE^E RRH_H RTM^M RWP^P RRQZQ RSSkS RYCYH RYKYP R_Q_[ RaGjGkOlR R[J_K RaJfL R`ScWlZ RbO`P R^SZWRZ RVJRK ReDcN',
	    'PoZGaG RRIYI RZKbK RZSaS RVCV[ R^P^X R^D^K RcJjJkXl[ R`MaQ R\\M[P RVJXP RdNgU RfRa[ RVIRU RaXYY RiSjZ RVFTQ RfDfMdW',
	    'PoRI[I R]JjJ R]NjN R]QaQ RfQjQ R\\TaT RfTkT R[WlW RWCW[ R]F]N RcCcF RdOd[ R]FjFjN RYMZQ RWKRU RWHTT',
	    'Po]FlF RRI[I R_MlM R`QjQ RWCW[ ReEe[ R`JjJjQ ReTjY R`SbU RWKZN RjSgV RWJRU ReV^Y RWGTS Ra[e[a[ R]F]S\\Z',
	    'Po[HaH RcIjI RRIZI RcMjM RZOaO Rh[l[ RWCW[ RcDcRiRh[ RcDjDjR RlXl[ RVKYR R^D^R_TaW RWHSU ReRcY R^M\\W',
	    'PoTG]G R_GjG R_JjJ RSJ^J R_MjM RRSlS R_Q_[ R_D_L R_DjDjL RfQkPkQ RXDXM\\N RWMTQ R`ScVlZ RbMaO^Q R]S[VRZ RfMfQ',
	    'Po[FlF RRI[I R]MkM R]PkP R^VjV R^ZjZ RWCW[ R]I]P R^S^[ RaCaP RfCfP R^SjSj[ R]IkIkP RWMZP RWHST',
	    'Po]GjG RRI[I R]JjJ R\\QkQ RWCW[ R\\M\\Q R]D]J RaMaQ RfMfQ R]DjDjJ R\\MkMkQ RWMZP R_TaWeYl[ RWHTT R[TjTgWcY[[',
	    'Po\\FlF R_IhI RRI[I R]PkP R]SkS R^WjW R[[l[ RWCW[ R]L]P R_C_I RdId[ RhChI R]LkLkP RWKZN RWHTT',
	    'Po\\FlF RSI[I R_PiP RaXgX Rh[l[ RWCW[ R\\L\\[ RaSaY RdCdF RdMdS RaSgShX R\\LlLl[ RWMZP R`GaJ RhGgK RWHST',
	    'Po\\FkF RRI[I R]IkI R[MlM R^VbV RdViV R^ZiZ RWCW[ R^R^[ RdRiRi[ RWM[Q RgNlS R`N[T ReIhQ RWJRU RbI_Q RbQ^R RWHTS RbF`O RbDaM',
	    'Po[KcK R[PcP RRSlS R[F[O R_P_[ R[FcFcO R`SdWlZ R^C_F R]SZWRZ RhDfIhK RjHfPkO RjMlP RVDTIVK RXHTPYO RXMZP',
	    'PoSIZI R\\KcK RfKlK R\\PcP RfRlR RWCW[ R\\G\\X R_C_F R\\GcGcO RWIZP RWHST RcR`TbWZY RfElEl[i[ RfEfReY',
	    'PoaFkF RRJYJ RbNjN RbRjR RVCV[ R[D[[ RbJbR RfCf[ RbJjJjR RhUlX RfQhU ReT`Y RVKYQ R^K_S RVIRV R[E_E^M R_U]U',
	    'PoRIYI RfSlS Re[l[ RVCV[ R^L^[ RgNg[ RcGfJ RhHlK R_E[H R^LZP RVIYO RbVdZ R_I\\N RbQcX RVISU RdDaL RiDgL RbP`Z',
	    'Po[FlF RRIZI R]OjO Rg[k[ RWCW[ R\\R\\[ R]H]O RjHjO R\\RkRl[ RVKZN RfThW RdKhM RcCdF R`HeJ RcO_X RgHdK`M RWGSS RgV_X',
	    'PoRIYI RcSkS RcVkV RVCV[ R]H][ R_H_W RcOc[ RVJYQ RcGhLlM R^EZN ReCbJ RdGkGfLaN RVHRU R^D\\M RcOkOj[h[ RVEUQ',
	    'Po]FkF RRIZI R\\KlK R]QjQ R]TjT Rf[l[ RWCW[ R]M]T RfTf[ R]MjMjT RlXl[ RcCcF RYLZP R`G`J RfGeJ RaT`Y[[ RWHTS',
	    'Po\\GlG RSIZI RWCW[ RdCdRfWk[ R]P`S RVIYO R]J`M RgPkT RhJkM RaO\\T RjIfN RbV][ RjOfT R`I\\N RWIRS RdP`X RWFUO RcMcU',
	    'PoSG[G RcGkG RSSkS RWCWP RgCgP RWH[L R^KaN RfHlM RaD\\I RaJ\\O R\\EaG RWFTM RgFcO R`TcXgZl[ R_Q]VZYR[',
	    'PoRIYI RaNkN RaSkS RiXkX RVCV[ RaJaX RfJfX RaJkJkX R[J]M RVNYQ R[D]F RdGhJ RaDkDgG RZP^P^XZ[ RVJST R\\Ya[l[',
	    'Po[HlH RRI[I R]KjK R]QjQ R]TjT R]WjW RWCW[ R]M]W R]MjMjW R^EjEjK RYNZQ RfXl[ RaX[[ RWHTT R^E]K RdEcK',
	    'Po]EkE R^HjH RSI[I R\\JlJ R^PjP R^TjT R^WjW RWCW[ R^M^W RdCdJ R^MjMjW RWM[Q RgYl[ RWJRU RaY[[ RWHTS',
	    'PoSIZI RaKlK RbTkT RbZkZ RWCW[ R\\F\\J R_L_[ RbOb[ RfKeO RbOkOk[ R\\FlFlI RXLYP RdCeF R_I[R RWHST R_J^P',
	    'Po]HjH RRI[I R]MjM R[UkU RWCW[ R]D]L RdUd[ R]DjDjL RhWk[ R_PaS RWM[Q RhRlV RdDdL_P RgN`U R_W\\[ RUPSU RWHTT',
	    'PocGlG RRJ[J R]YkY RWCW[ R]K][ RcCcK R]KkKk[ RdVeW R_RaS RXNZR R`OgV RbMdN RgQiS ReQ_W RfMcS RWHSU',
	    'Po[SkS RWCW[ RcQc[ RaEiEiM RcRjY RWMZP RcR\\Y RkGkL RRI[I]H R\\D^E R]K[O RaG`K RhOdO RWHSU RdEcM',
	    'PoRJZJ R`RhR R]ZjZ RWCW[ R\\F\\I R]T][ RaKaQ RcCcF RcScZ RhOhR RjTj[ R\\FkFkI RWKYN RaHdJ RiKlO R]K\\O RhIbO[R RWHST',
	    'Po[HlH RRI[I R]KjK R]QjQ R[TlT RWCW[ R]N]Q R]E]K RdCdQ R]EjEjK R]NjNjQ RYNZR RaQ_U^Xk[ RWHST RhVeY[[',
	    'PoRJ[J R]MjM R]QjQ R\\TlT RWCW[ R]J]Q RdJd[ R]JjJjQ R]F_H RbFeH RhFjH RjChF R_C]F RdCbF RYNZQ RdThXlZ RdT_X[Z RWHTT',
	    'PoZGcG RRIZI R[QcQ RWCW[ R[K[Q R_C_[ R[KcKcQ R^QcV RWMYP R]TYX RhTk[ RgIgSdZ R_P\\V RgOfW ReIkIkO RVHSU RfCdO RgNiW',
	    'PoZHdH RWMgM R^PlP RRR\\R R_WfW Re[i[ RWJWM RXOX[ R_S_X R_SfSfW RWJgJgM RiPi[ RXS\\W RVTRX RXQUV R`ElH R_DYGRI',
	    'Po]DkD RRI[I R^JaJ RfJiJ R]MaM RfMjM R]PkP R\\SlS Rd[h[ RWCW[ R\\H\\K RdDdN R\\HlHlK RWMZP R`S_VjVh[ RWHST',
	    'PoRJ[J R_QiQ R]UjU R\\[l[ RWCW[ R\\I\\M R_M_Q RdQdZ RdCdI R_MiMiQ R\\IkIkM R^D`G RiDhG RYMZP RWKRU RWHTT',
	    'Po\\ElE RRI[I R]MkM R^PkP R\\TlT R`[d[ RWCW[ R]H]M RbEbM RdTd[ RfEfM R]HkHkM RWMZP RhVlZ R_V[Z RWKRU',
	    'Po\\FlF RRI[I R\\OkO R_TdT Rf[l[ RWCW[ R_Q_Z RaHaO RdCdF RfQf[ RfHfO R\\JkJlO RlXl[ RWMZQ RUPRU RkSfU RWHTT RdY\\Z R\\F\\SZ[',
	    'PoRIZI RWCW[ RdDkDkM RVLZO RiIfL R_F[G R`I]L RhFdG RdMfOlR R[DbDbM`P[R RdQ^T RgS_W RWHST RiWdY][',
	    'Po]GlG RRI[I R_MlM RbSiS RWCW[ RbIbS RcVcZ RdCeF RiIiS RWIYO RfVhZ RjVlZ R_V^[ RWGTS R]G]R\\Y',
	    'PocEjE RRI[I R`KhK R^LeL RcNjN R`SjS R`WjW R][l[ RWCW[ R`P`Z RcIcN RcCcH R`PjPjZ RWLZP R\\HlHkK RWKRU RWHTS R\\H\\S[Z',
	    'Po\\DlD RRIZI RbMhM R_VdV RfVkV R\\ZlZ RWCW[ R\\D\\[ R_P_V RbHbM R_PdPdV RfPfV RbHhHhM RfPkPkV RXNYQ RWHSU',
	    'Po]FlF RRI[I R\\JlJ R^PjP R^SjS R\\WlW RWCW[ R^M^S R`FaJ RdCdF RdSd[ R^MjMkS RWM[Q RWJRU RhFgJ',
	    'Po[FlF RRJ[J R_JhJ RWCW[ R\\M\\[ R_C_J RcJc[ RhChJ R\\MkMk[ RWJXNZQ RhReW R`PaW R`R]W RhPiW RWHST Rh[k[h[ RgPeP',
	    'Po[FlF RRIZI R]MjM R]QjQ RZUlU RWCW[ R]J]Q R`C`H RgCgH R]JjJjQ RVIYP RcTgYl[ RVHST RcQaW_YZ[',
	    'Po]IjI RRI[I R]MjM R]PlP Rf[j[ RWCW[ R`V`Z RcCcF R]FjFjL RcUdY RWMZP RfUhW RWKRU R\\VZZ RWHTT R]F]SkSj[',
	    'Po\\FkF RRI[I R^JjJ R\\MkM R\\TaT RWCW[ RdFdM R_CaF RaU\\Z RjRfV RhCfF RYNZQ RaOgP RdShYlZ R^QdQd[`[ RWHRU',
	    'PoSIZI RaSlS RVCV[ R]H][ R_H_V RfOf[ RhVlY RdV`Z RVKYQ RgSiX RcGdJgMlO RfQdV R^CZM RdCaJ RcGjGhJdNaO RVISU RVFUQ',
	    'Po[FlF RSJYJ R_KiK R]RjR R^UjU R\\XkX RVCV[ R`C`H RcPc[ RgCgH RVKYQ RaI\\L R_LeOlQ RVIRV RhLbO[Q',
	    'Po]GcG ReGlG RRIZI R\\NkN R]TjT R[[l[ RWCW[ RdNdZ RXNZQ RbKdM R_FaI RhFiJ R^C\\I RfCdI RWHSU',
	    'PoRJXJ RjLlL R[PbP RVCV[ R^L^[ ReDeK ReDjDjL RgVl[ R^QbU R`LaN R\\EbJ RiUc[ RbE\\K RVKXP RdPhX RdOkOgW R^OZW RVIRU RVGST',
	    'Po]IkI RRI[I R_LcL RgLkL R_OcO RgOkO R`TkT R^WlW RWCW[ ReJeP ReQe[ R]DkDkI RWM[Q RVMRU R`Q_U RWHTT R]D]R[Z',
	    'Po_GkG RRI[I R\\KlK R_OlO R_RkR R_VkV R_ZlZ RWCW[ R`N`[ RfMfZ RWK[O R`C]I RXIRU RdG`N[S',
	    'Po\\FkF R]RkR R]VkV RWCW[ R]N]V R`C`J RdKdV RgCgJ R]NkNkV RWMZP RgXl[ RWKRU RaX[[ RSJZJ R\\KlK',
	    'Po\\DcD RRJZJ R\\LcL R\\PcP R\\UjU RZZlZ RWCW[ R\\D\\P R_D_H R`L`O R\\HcHcL RcRcZ RXNZR RWHSU RfElEjKfP RfIgMmP',
	    'PoSJZJ R]QjQ R_[h[ RWCW[ R]F]Q R`T`Z R]FjFjQ RcSeV RjTlY RbLfM RiXh[ RbCbF RbH_K RVJXNZQ R\\U[Y RaIgJcN`O RWHST',
	    'Po\\GkG RSI[I R^JhJ R^MhM R[QlQ R_VhV R[[k[ RWCW[ RcScZ RWIYO RhCgQkV R^C_QZV RWIRS',
	    'Po\\ElE RRI[I R\\OkO RWCW[ R\\I\\O RaEaO RfEfO R\\IkIkO RWMZP RkRhU ReVhYl[ RVMRU R^R\\U RbV`Y[[ RdPeX RWHTT RcPbX',
	    'Po\\FkF RRIYI RZLlL R_WgW R\\ZjZ RVCV[ R\\P\\[ R_S_V RcCcL R_SgSgV R\\PjPj[ R]H`J RiHgK RVIYP RVHRU',
	    'Po]DjD RRIZI R]KaK RfKiK R]NaN RfNjN R[VlV R]ZjZ RWCW[ R[H[K RcDcP R]RjRj[ R[HkHkK RWMZP RWHSU',
	    'PoRIZI R[TlT RWCW[ R\\D\\M R\\DbDbM RdDdM RdDkDkM R_NbO RhNkQ RXKZO RaQ_V R\\PZQ RePcP R^Wk[ RWHST RhUcY[[ R_G^O RhFgO',
	    'Po[GlG RRI[I R\\JdJ R\\MeM R\\RdR R\\TdT RWCW[ R\\OdOdW R\\O\\WcXe[ RYNZQ RiDkF R^X[[ RlXl[ RUOSU RWHTT RgChMgQiZ',
	    'Po\\HlH RRI[I R^KiK R\\NbN RdRkR R[UlU Rc[g[ RWCW[ R_N_R RdNdR RgSg[ R^DiDiK RdNkNkR RWMZP R_WaY RWHSU RbQ[R',
	    'Po[GdG RRI[I RZMlM R\\TcT R\\XcX RWCW[ R\\P\\Z R\\H]K R\\PcPcX RWM[Q RiFkI RhUb[ RjOfW RUPRU RbHaL R_C_G RWHTT RlWk[i[ RfCfOhZ',
	    'Po^HkH RRI[I R^LkL R^OkO R^TlT RWCW[ R_G_S RaWa[ ReFeS RWM[Q R_H[K RfWf[ RiVk[ R`C]J R]V\\[ RWHTT ReDeK',
	    'Po]HjH RRJ[J R]KjK R[NlN R\\RbR R\\UbU RWCW[ R\\N\\Y R]D]K RbNb[ R]DjDjK RfVk[ RXNZR RiVd[ ReRhX RdQkQhX RWHSU RbX[Y',
	    'PoRF^F R_FkF RROlO RUR]R R_RjR RUU]U RUOUY RXCXM R]O][ ReCeM ReFkL RYFRL ReF_L RWFYH]J RbTfXl[ RjSeX_[ R\\XSY',
	    'Po\\IlI RRI[I R\\NkN R]QkQ R[UlU RWCW[ RaCaI RfCfI RWMYP RjDiG R`J`M R]D^G RdNcUhYl[ RhJgM RcV`Y[[ RWHST',
	    'PoZFdF RRIZI ReJlJ R[JcJ R[RcR Rf[j[ RWCW[ R[M[R R_C_J R[McMcR RjCj[ RVKYQ R]T]X RbTaY RfNgT RVHSU RdYZZ',
	    'Po\\FlF RRJ[J R\\OlO R\\SlS R\\WlW RWCW[ R_K_S RaCaI RdKd[ RhChI R]KjKiS RWM[Q RWKRU',
	    'Po^IjI RRI[I R[LlL R^QjQ R^TjT R^WjW RXCX[ R^E^I R^N^W RdCdL R^NjNjW R^EjEjI RXM[Q RWJRU RgYlZ RWHTS RaY[[',
	    'Po\\HlH RSIZI RaJjJ R_MlM RbPeP RfSlS ReWlW R^[`[ RWCW[ RaUa[ R\\DlDlH R^ScS`U RVJYQ RWHSU RfLeP RcV]X RiUi[e[ R\\D\\Q[Y',
	    'PoRIZI ReMkM R]PkP R]RkR RZUlU Rc[g[ RWCW[ R]J]R R_GgGfM RgRg[ R]JkJkR RWMZQ R^WaY RaDhElI RbGbL_N R^F[I RVHSU',
	    'Po[GaG RcHkH RRIZI RZLbL R`ZlZ RWCW[ RcHcT RlUkX R\\V_Z RiDkF RhSdX R[R\\X RkLgU RfRaU RVISU R^QaQ^Q RVGTS RhChLiW R_C_YeZ RVIZPYZ',
	    'PoSI[I R\\JbJ ReJkJ R]PkP R]SkS R[WlW RXCX[ R\\D\\J R]L]S R\\DbDbJ RdLd[ ReDeJ R]LkLkS ReDkDkJ RXN[Q RVLRU RWHTS',
	    'Po\\FkF RSJYJ R]JjJ RZ[l[ RWCW[ R\\S\\Z RaSaZ RcKcQ ReSeZ R\\SjSjZ RhKhQkPkQ RVKYP RcCcF RgGjJ R^N\\Q RaF]J R^K^P RWISU',
	    'PoeGlG R\\GcG RSIZI R]SeS R]WeW ReZiZ RWCW[ R]O]Y R]OeOeW R_F`I RVIYP RgFhI R]D[I R^J\\O RfCcI RVHSU R]LjLjUiZ',
	    'Po\\FlF RRIZI ReMjM R]OjO R]RjR R[VlV RWCW[ R]J]R RfFfM R]JjJjR RYNZQ R`DbFbK_N RhDeG RcVfYk[ RdVaY\\[ RWHST RdRcX',
	    'Po\\EbE RRJZJ R\\QaQ RcTjT RWCW[ R]LaLaQ RdLiLiQkPkQ RXNYQ R\\G_I ReFjK RhDfG RjGhI RaF[L ReDgH RdUgYk[ RjUfYb[ RWHSU R`[][ R\\Q[UaU`[ RdLcR',
	    'Po\\DcD RRIYI R\\NbN R\\SbS RVCV[ R\\J\\X R_D_I R[JcJb[ RVKYQ RgRkZ RiRfZ RfMiX RVHSU RfIhV RfCdN RfIkIiV RbWZX',
	    'PoRIYI RZMlM RZTlT RVCV[ R^F^T R`W`[ RaGaT ReGeT R]GjGiT RiWkZ RVJYQ RdWf[ R^C[J R\\WZ[ RVHRU RVETQ',
	    'PoVGjG RSKlK RSOkO RSS^S R`SlS RXEXO RYPY[ R\\F\\O RbFbO RePe[ RgFgO RfTlZ ReRdU_Z RYCSI RYSRZ RYU]W',
	    'PoRJ[J R][b[ RWCW[ R^P^[ RbEaNjMjN RgQg[lZl[ RVKZO RWHSU RaPZQ RjPcQ RgC\\E RaSZT RkScT RbVZW RhF\\H RgJ]K RiI_K RlVcW',
	    'Po^FjF RRJ[J R\\JkJ R\\McM ReNkN RdQkQ R[TlT RWCW[ R`K`Q RdCdJ RhKhQ RWMYP RaT_Y[[ RgTg[lZl[ RWHST RcP\\R',
	    'Po[HlH RRIZI RcQlQ RcWlW RWCW[ RcCcO RdScX RhOh[ RWMZP R]D_G RjDgG R\\S`V[[ RcHfJlM R]O[T R]QbQ^X RcH`KZN RWHST',
	    'Po[DlD R\\HaH RRJ[J RfMkM R\\MaM R\\UkU R\\ZkZ RWCW[ R\\H\\M R\\Q\\[ RaDaQ RcQcZ RfDfQ R\\QkQk[ RfHkHkM RWMZP RUPRV RWHTT',
	    'Po\\HlH RRJ[J R_OhO R`XgX RWCW[ R`T`Z R`TgTgX RWMZP RfHkM RWHST RjDdE]E RdE\\N R_KhKhO_O_K R\\[\\RkRk[h[',
	    'Po]HcH RfHlH RRJ[J R]LcL RfLlL RbRgR RbVgV ReZgZ Ri[l[ RWCW[ R]E][ R]EcEcL RfEfL RbOgOgY RfElEl[ RWMZP RWHTT RbObUaZ',
	    'Po]HcH RfHlH RRJ[J R]LcL RfLlL R`OiO RaShS R`WiW Ri[l[ RWCW[ R]D][ R]DcDcL ReOeW RfDfL RfDlDl[ RWMZP RUPRV RWHTT',
	    'PoRJ[JlK R]ViV R]ZiZ RWCW[ R]R][ R\\EcEdP RcRcZ R]RjRj[ RWKZO R^G_I RhFgJ RcJhOlP RcJ^PZQ RWHST RiC`E',
	    'Po]GkG RRI[I R]JkJ R\\MlM R\\RlR R^ZjZ RWCW[ R^U^[ RdGdR R^UjUj[ R_C`F RhCgF RVIYN R_N`Q RhNgQ RWGSS',
	    'PoSEkE RWJhJ RVNhN RYSgS RRUlU RSLSO R_C_L R_S_[ RXQgPgS RWGWHhGhJ RSLkLkO RZNZPSR R_UTZ R_UfYlZ',
	    'Po\\GlG RRI[I R^PiP R^TiT R`[c[ RWCW[ R_L_T RcTc[ R]MiLiT RWM[Q RhVkY R\\I_K ReHkN RkHhK R_V[Z RaI[O RVLRU RcC_K RWHTS',
	    'Po]DkD RRIZI R^JaJ RfJjJ R^MaM RfMjM R^QjQ R\\TlT R\\ZkZ RXCX[ R\\H\\K RdDdN R\\HlHkK RgVl[ RXN[Q RWJRU RaT_Z RWHTS',
	    'PoRJZJ RePlP RbVlV RWCW[ R[K[N RePeV RiMi[ R[KkKkN RXNZR R]S`V[[ R^F^I R[EbFcI R^M[T R]PbP^X RiFgK RWHSU RjC`E',
	    'PoZGdG RRIYI R\\NcN R]VdV RZVaV R\\[_[ RWCW[ R\\J\\N R_T_[ R\\JcJcN RWMYP R[QcQ_T RgWc[ RgRkZ RjQfY R_C_G RfNiX RWHSU RfKhW RfJkJhV RWFTS RgCdO',
	    'PoRIZI R_OiO R^QkQ R[UlU Rd[h[ RWCW[ R\\H\\K R_J_O RdCdG RhQh[ R_JiJiO R\\HlHlK RWMZP R^D_F RjDhF RUPRV RWHTT R^Q^U RhUaY[[',
	    'PoRIZI R^PiP R[TkT RWCW[ R\\H\\K R^K^P RcCcH RcPc[ R^KiKiP R\\HkHkK RWM[Q R^D_G RiDhG RcShXlZ RVMRU RcS^Y[Z RWHTS',
	    'Po[FcF ReFlF RRJZJ RePkP R\\PbP RdUkU R\\VbV Rg[k[ RWCW[ R\\K\\X R`C`I R\\KbKbU RhChI ReKkKk[ RXNYQ RWHST ReKeSdX',
	    'PoRI[I RaNdN RfSlS RaYhY Rh[k[ RWCW[ R]P][ RaUaZ ReIeN RfPfS RaUhUhY R]PlPl[ RWMZP R]IlIiL R^DjDdG RcJ\\M RbPaS^T RaFiI RWHTT',
	    'PoSI[I R`KhK R^TiT R[ZlZ RWCW[ R^O^T R^OiOiT RWM[Q RiDfG RkHhI R`V`Y RgVfY ReDgIlM R]EbE`I\\N R]G`H RWISV',
	    'Po[HlH RRJZJ R\\KjK R\\Q`Q RfQkQ R\\TkT RfVkV RWCW[ R\\M\\W`V`[ R`M`Q RcCc[ RfMfQ RfVf[ R\\EjEjK RkMk[ RXNYQ RVHRV R\\Q[Y',
	    'Po[EkE RRIZI R]LdL R^TdT RWCW[ R^N^U R^G_K RdNdU RfLlLkQ RVIXO RiLiUk[ RhTeZ RcGcK RVHSU RfGeQ R[E[SYZ RaLaT_Y RhOgW',
	    'PoUDlD RWIaI RXN`N RSTlT RXKXN R_Q_[ R`K`N RfMkQ RfMbQ R^F^I R\\I\\OXR RaUlZ RcIkIjL RdFbK R]USZ RYFYH RfIeN RUDUKTP',
	    'Po[DlD RRIZI R\\LkL R]QjQ R]TjT R[WlW RWCW[ R\\G\\K R]N]T RaDaK RdTd[ RfDfK R]NjNjT R\\GkGkK RWIYO RWJRT RWGTS',
	    'Po\\FkF RRI[I R]LkL R]OkO R\\SkS RWCW[ R]I]O RdCdR R`U`ZhYgZ R]IkIkO RbTeW RWIYMZP RhQlT RjUkZ RWJRU R\\V[Z RWGTS',
	    'PoRJ[J R[QlQ RWCW[ RVIYO RgOjP R]TbV RiTeY`[ RWHST R^Q[Z R^D\\H^J RaG\\NaM R`LaN RhDfHhJ RjGfNjM RjLkN RdCdMfYi[l[mX',
	    'Po[FlF RSJYJ RVCV[ R^V^[ RbPeQ RiUfW RVKYQ RcThYlZ RcG\\J RVIRV R`IhIcK\\M ReM\\P RcNkNeQ\\T RcTZW RdY]Z RVFTR',
	    'PoRIZI R]NkN RWCW[ R^H^N R\\JkIkN RWMZP RgTlY RjRfS R`E[J RdMhV ReN\\R RcR[V RaPdReVbW[Z RWHST ReWe[a[ R`FfEdJeQ',
	    'PoaFlF ReIkI RRJZJ RcMlM RcSkS RcWkW Rh[k[ RWCW[ R[D[[ RcPc[ RcPkPk[ RVKYQ ReDaL RhIhM RVHSV R[E`E^L`R`U]U',
	    'PoRJZJ R`MhM R]SkS R]WkW Rg[k[ RWCW[ R]P][ RaIaM RdPd[ R]PkPk[ R\\FlFlJ RXMZP RbHeI RcDdF RiJlL R\\F]J\\M RWHSU',
	    'PoRTlT R_R_[ R[EZJdIdJ R\\M]O RWKYL RdKgL R\\D`E RjMkP RfEjI RaM`Q RVGTI RaVkZ RTMRQ RWLWQ[P[R R]VR[ RdMcQhPhR',
	    'Po\\EfE R[HlH R_NiN R_QiQ RRTlT RYHYO R_K_Q R_R_[ RbCbH R^LiKiQ RUDYHTM R`TcVkZ RbKZL RjDgG[L R\\URZ RZDXFRH RXQUQ RXFYM',
	    'Po]FkF RRJ[JlJ R^PkP R^TkT R]WkW R[[l[ RWCW[ R^M^S RdCdF RdMdZ R^MkMkS RWM[Q RaFaJ RWJRU RhFgJ RWHTS',
	    'PoRIYI R\\PjP R]ViV R]ZiZ RWCW[ R]H]P R]S][ RcJcP R]SiSi[ R[JkIkP RWMZQ RaEgDlJ RgKfN R_E[J R_K`N RWHSU',
	    'Po]FkF RSI[I R\\IlI R^PjP R^SjS R^WjW RWCW[ R^M^W R`H`K RdCdH RhHhK R^MjMjW RWM[Q RgXl[ RUOSU RaX\\[ RWHTT',
	    'PoRIYI Rg[l[ RVCV[ R[N[Q R`RgRg[ R[NkNkQ RlXl[ R_HbK RgHkL R_W[[ RbEaG RVJYP RkEjH R\\E\\H ReEeH RfKdK RVHST R_C^J RhCgJ R`R_X',
	    'PoSIZI RcJlJ RWCW[ R^IaL RiDkG R[L_O[T RVJZR R`Wa[ ReVf[ RiVk[ RhLkT R^EZM RbI^R R\\WZ[ RcSaT RgCfJiR RWHSU R]GbG`P RfFeQ',
	    'Po[FlF RRI[I R`JgJ R[LlL R]SjS R]VjV RWCW[ R]O]V R`C`J RcLcV RgCgJ R]OjOjV RWM[Q RgXkZ R`XZ[ RWHST',
	    'PodDkD RRIZI R[JcJ RdJlJ R]VjV R]ZjZ RWCW[ R\\F[J R]R][ RdFdJ R]RjRj[ RiLiPlOlP RgLcP RWJYQ R\\DaDaN_O RWHST RhDgL',
	    'Po[FlF RRJ[J R]LiL R\\OkO R[TlT R]WkW RWCW[ RcCcH RcTc[ R]HiIiL R_P`R RcLcO RhQeS RWJYP R]I]L RWHST',
	    'PoRJ[J R]QjQ R]TjT R]WjW RWCW[ R\\E\\L R]N]W R]NjNjW RXNZR RgXl[ RaGaJ\\K R`Y[[ RWHSU RgEeK RjLgL RbD\\D RdDkDjL',
	    'PoSIZI Re[i[ RWCW[ R]N]Q R]NlNlQ RWIYO R`HcK RhHlL RkEjH RWJRU R]E]H RcEcH RfEeH R_K\\L RbV_Z\\[ RWGTS RaC_J RcPbW RhCgK R]SjSi[',
	    'Po]HjH RRIZI R]KjK R]NjN R[WlW RWCW[ R]F]N RbCbF RdOd[ R]FjFjN RVIYP ReSkT RWHST RkPeQ RbS\\T R\\QaQ',
	    'Po\\ElE R^IdI RRIZI ReMlM R^RdR Rg[j[ RWCW[ R^L^R R^T_X RcTbX R^LdLdR RjGj[ RWMZQ RfPhU RWISU R\\E\\RZZ ReY]Z',
	    'Po\\EkE R]IjI RRI[I R^OjO R[SlS R^ZjZ RWCW[ R^V^[ R^K^O RdCdI R^KjKjO R^VjVj[ RYNZQ R`Q`S RgPgS RWKRU RWHTT',
	    'PobIjI RRIZI RaMlM RbSjS Re[i[ R\\[_[ RVCV[ R[D_D_[ R\\Q^S RVNZQ R\\J^L RjDkH RgIkR RcDaH RfIbR RcZa[ RVISU RfCdN RfPdY RjTi[ R[D[SY[',
	    'PoRIZI RfKlK R]PdP RbSjS R]TfT R`[c[ RWCW[ R\\E\\K R`C`K RcScZ RfCfK RXLYP RhVkY RiQjT R^V[Y RaN]O RfO\\T RWHST RcF`G RkFfG RcJ[K',
	    'Po[GbG RRIYI RZKcK R[RbR R[VbV R_[b[ RVCV[ R[N[[ R\\C\\K RaCaK R[NbNb[ RVJYQ RgRjZ RiQeZ RVHRU ReJhV RfIkIhW RfDdN',
	    'Po[GbG RRIYI R[NbN R[RbR RZVcV RVCV[ R[J[R R_C_J R_R_[ R[JbJbR RgVcZ RVKYQ RhEjI RkNfX RlWk[ RVIRV RgDgNhZ RkKdK',
	    'PoSJYJ RaLfL RaQfQ RhQlQ Rf[l[ RVCV[ R^D^R RaFaQ RaFfFfQ RgSgZ RiDiQ RlNkQ RlWl[ RkGhJ R`W\\Z RcCbF RVKYQ R`T^Y R^NZO RVIRV R[H^H[H RVGST',
	    'Po^IjI RRJ[J R^NjN R\\UcU ReUlU R\\ZcZ ReZlZ RWCW[ R\\P\\[ R^E^M R\\PcPc[ RePe[ R^EjEjM RePlPl[ RWMZP RWHTT',
	    'PoSIYI R^KkK R^NkN R\\QlQ R^WjW R^ZjZ RWCW[ R^G^N R^S^[ ReHeN R^SjSj[ R\\IkHkN RaC\\I R`FfEdI RVIYP RVHST',
	    'Po\\HbH RfHlH RRIZI R\\MbM RfMlM RaSgS RaWgW Rh[l[ RVCV[ R\\D\\[ RaPaY R\\DbDbL RfDfL RaPgPgW RfDlDl[ RVKZN RVHRU',
	    'PoRIZI R]IiI R[NkN R[QaQ RdQkQ R[UaU RdUjU R[ZaZ RcZlZ RWCW[ RVKZN RWGSS R]Q\\Z RaE_N RjSjZ RaR`Y RfQeZ R\\EiEhN',
	    'PoSIZI RcIiI RaLlL RVCV[ RVIYP R^DZM R`H[S RfLdOaP R_VZX RgTdW`Y RjOhRkY RVHRU RdCcI R_RZS RfQcTaT RhRgZd[ RdFjFiL R_LZM RgPhX',
	    'Po`FjF RRIYI R_JkJ R_NkN R`RjR R_UlU Ra[l[ RVCV[ ReCeI ReNeY R[F]H RhKgN RVJXP RbKbM RZN]N]XbZ R]XYZ RVHRU RVFTS',
	    'Po_IhI RRIZI R\\PbP RePkP R[SkS RWCW[ R\\K\\P R_D_I R\\KbKbP RcQc[ ReKeP R_DhDhI ReKkKkP RWMZP RcRhXlY RcR^XZY RWHSU',
	    'Po\\ElE RRJ[J R^JjJ R^MjM R\\OlO R]UjU R]XjX R[[l[ RWCW[ R]R]X R^G^M RdGdM RdRdX R^GjGjM R]RjRjX RWMZP RWKRU',
	    'Po\\FlF RRJ[J R`MgM R]OjO R^UjU R^XjX R[ZlZ RWCW[ R]H]O R^R^X R`J`M RdCdF R`JgJgM R^RjRjX R]HjHjO RYNZQ RWHTT',
	    'Po[FkF RSJZJ R`MfM R\\OjO RZUlU RWCW[ R\\H\\O R`J`M RcCcF R\\RdRd[ R`JfJfM R\\HjHjO RWJYP RdUfXlZ RbU_XZZ RWHST RiQbR',
	    'Po]HjH RSI[I R]LjL R]NkN R_QjQ R]TkT RWCW[ R]E]L R`T`[ RbCbL RfCfL R]EjEjL RYLZP ReUhYl[ RWJRT RjUfW RWGTS RdZ_Z R]N]U\\[',
	    'Po^EfE R^HiH R\\JkJ R\\OkO R]QiQ RRTlT RTDTR R_R_[ RcHcJ R]LiLiQ RhEjH R^E[G RYJZN RaVjZ RTEZEXJ R]VR[ RZOWP RhDbD',
	    'PoRIYI R[JbJ R[MbM RZQcQ RVCV[ R[F[M R^C\\FbFbM RgWc[ RVJYP RfQjZ RiQeY R[ZY[ ReMhW RjMgW RVHSU Ra[^[ ReJgU ReIkIhV R^M^Q\\Y RfCdN R]UbUa[',
	    'PodEjE R[IlI RRJZJ R\\MkM R^PdP RWCW[ R_E_I RdCdI RlXl[ RXNZR RaPbW^Z RhUc[ RVKYP RhJkK RdRaX RjPgW R_S]V RWHSU RfIfNhY R\\M\\RZY',
	    'PoSJYJ RgJlJ RdZkZ RVCV[ RdQd[ RgCgQ RdQkQk[ R^UbY R^IcM RbP`T R]WY[ RcFaI RVJYQ R\\F[I R\\QZU R\\MZN RVIRU R_D^L R^N]X',
	    'Po]GlG RSIZI R]JkJ R]MkM R]PlP RaWgW Rg[l[ RWCW[ R\\S\\[ R^F^P ReEeP RgSlSl[ RWJYP R_C\\J R\\SaSbW RgSfW RWHTS',
	    'PoRIZI R\\IkI R[LlL R]SiS RVCV[ R\\D\\I R]N]S R^V^[ RaDaI RfDfI R]NjNjS R\\DkDkI RVJYP RdUhYl[ RbTZX RkUfW RVHRU RdZ]Z RVFTS',
	    'Po]HcH RRI[I R]KcK RfNlN R]VkV R[ZlZ RWCW[ R]E]N R]RdRdZ RfFfN RfFlFlN RWM[Q RUPRU RWHTT R[EcEbO\\N RiP`R',
	    'PoZGbG RSJYJ RdMkM R[NaN R[RaR RcSlS RYVbV RVCV[ R[J[R R^C^J R^R^[ R[JaJaR RgMg[ RWLXO RgCkK RgCcL RVIRV RVGTT',
	    'Po\\FjF RRI[I R[JkJ R_NhN R^SiS R^UiU R\\XkX RWCW[ R^P^U RcLc[ R^PiPiU RWK[N RgDgKlP R`D`K[Q RVLRU RWHTS',
	    'PoRIYI R\\JkJ ReNlN R[RaR ReRkR RZVaV ReVlV RWCW[ R\\E\\J RaEaJ R[NaMa[ ReLe[ RfEfJ R\\EkEkJ RVJYQ RVHRU',
	    'Po]FkF RRJ[J R\\JlJ R^PjP R^SjS RWCW[ R^M^S RdCdF R^MjMjS RbUeV RiVlY RYNZQ R`F`J RUPRV R]V[Z RhFgJ RaVa[hZh[ RWHTT',
	    'Po_IiI RRI[I R^PjP R_XhX Rh[k[ RWCW[ R]S][ R^K^P RdGdI RjKjP R]SkSk[ RYNZR RfUhY RaKgN RdCgFlH RdC`G\\I ReL`N RWHST RcPaX',
	    'Po\\HlH RRJ[J R\\MkM R]QjQ R\\TlT RWCW[ RbCbH RcMc[ ReCeH RXNZR R]D_F RjDhG RgIfL R_I`L RcThXlZ RcT_XZZ RWHST',
	    'Po]IlI RRI[I RgLlL RaNiN R]PlP R`RjR R`UjU R`ZjZ RWCW[ R`W`[ RfHgL R`WjWj[ RWMZP R`EgEdH RcHbK_L RWHST R`C_G\\J]R\\Y',
	    'Po]EkE RRIZI R^KbK RfKjK R]NbN RfNkN R]VkV R]ZkZ RWCW[ R\\H\\K R]R][ RdEdP RdRdZ R]RkRk[ R\\HlHlK RWM[Q RVLRU RWHTT',
	    'PoRF^F R_FlF R\\ZfZ RWZaZ RXCXJ RXO_O^Y RYRdRdU ReCeJ RYRYVdVg[ RXETJ RVF]I RlXk[l[ ReEhHlJ ReEbH_I RULhLiUi[ RULUSTY RdN^N',
	    'PoRIYI RZI]I R_JbJ RiLlK RZNbN RZRbR RVCV[ R[E[N R^R^X R_EbEbN ReDiDjL RfUl[ RiTbZ R]DZE RVJYP RdPgW RcOjOgV RVHRU RbWYY RVETQ ReEdL',
	    'PoRJ[J R_PjP R]VkV R]ZkZ RWCW[ R\\H\\K R]R][ R_K_O RdCdH RdRdZ R_KjKjO R]RkRk[ R\\HlHlK RWMZP R^D_G RiDhG RWKRU RWHTT',
	    'PoRE^E RhIlI RTK]K RRM_M RRTlT RTGTK RXCXP RTNTQ]O]Q R_Q_[ RbLkP R`TcWl[ RaJjJgNaP R^T[WR[ RbDhDhI RXI\\I RUIYI RbDaI RWISI RTG]G]JZI',
	    'Po`HlH RRIYI Ra[l[ RVCV[ RhOhW RgOlT RbCcG ReHaM RZM]M]XZZ RVJYQ R[E]I RiCgH RfMcP`Q RkKgOcU_V RVHRU RgYcY RfMhU RVETQ',
	    'Po`FkF RTI]I R_JlJ R`NkN RUP]P RRTlT RUKUP RTD]D]H RUK]K]P R_Q_[ ReCeF ReJeQ RbGbI R`TcWl[ RhGhI R]T[WR[ RTFSO',
	    'PocGlG RRIYI R\\LbL RcNlN RdTlT R]YbY RVCV[ R\\E\\Q][ R\\EbEbK R]QbQbY ReIeL RhNh[ RgDgG RVJYQ RkIhN RVHRU R\\LZW',
	    'Po`GhG RRJYJ R`JhJ R`NhN RfQlQ RZTbT RVCV[ R`D`N R\\F\\PbQb[ RfQf[ R`DhDhN RlFlP RfTlTl[ RVKYQ RVIRV R]T[Z',
	    'Po[FbF RdFlF RRIZI RcUkU Rb[l[ RVCV[ R_C_N RdPdZ RhChN RVKXNYQ R^FbJ RjHkL R[PkPjS R_E\\K RhEeL RVIRU R]S[Z RVFTQ',
	    'PoRIZI RcUhU RhXkX Ra[l[ RVCV[ R`M`X RcPcV RfHfM RcPhPhU RbDiDiM RbDaMkMkX R[K]M R[D^G RZQ]Q]WYZ RVIYO RVISU RiHeH R_Zf[',
	    'PoRIZI R]QjQ R^WiW R^ZiZ RWCW[ R]K]Q R^S^[ RdKdQ R^SiSi[ R]KjKjQ RYNZQ R`M`O RgMfO RcC_H[I RWHST RdChHmI R`IgI',
	    'PoRIZI R\\IbI R\\PbP R\\SbS R[WbW RYX_X RiZk[ RWCW[ R\\D\\I R\\L\\W RZLcLb[ R\\DbDcI RWMZQ RhEkH RhVdZ RkNgX RlWk[ RVHSU RkJ`L RgCgPhZ',
	    'PoRIYI R[ObO ReQkQ R[TbT RdVlV R_[b[ RWCW[ R`J`T R[KbJb[ RhNh[ RWNZQ R]FaF_L RWHSU RgEeK ReNdT RkLhL RWFTS RdEkEkL R^C[J[TZ[',
	    'Po[EkE RRI[I R\\LkL R]QjQ R]TjT R]WjW RWCW[ R\\H\\L R]N]W RaEaL RfEfL R]NjNjW R\\HkHkL RWM[Q RgYl[ RUPRU R`Y[[ RWHTT',
	    'PoRE`E RbGlG RUOZO RZQ]Q RRTlT RUKUO RUKZKZO R\\C\\G R_Q_[ RVGRK RcGdLhOlP R`TcWl[ RVCVG RdCaI RiGgLdN`P R^T[WR[ RTI^I]Q',
	    'Po\\IkI RRI[I R]LjL R[PlP R[TlT R\\WkW RWCW[ R\\E\\I RaEaJ RdJdP RdTd[ RfEfJ R\\EkEkI RWMZP R`Q`T RhPgS RWKRU RWHTT',
	    'PoRI[I R]RbR ReRkR RWCW[ R]M]R R]MbMbR ReMeR ReMkMkR RgWk[ R_VbY RdDjJ RdD]J RYNZQ RWIRV RaJgJ R_T^X[[ RgTfXc[',
	    'Po\\FkF RRI[I R[NlN R`XgX R]ZjZ RWCW[ R]Q][ R`T`W RcCcN R`TgTgW R]QjQj[ RWMZP R_IaL RhIkM RhGfL R_G]L RWHST',
	    'Po_GlG RRIYI RaOjO RaSjS RaWjW RaZlZ RVCV[ RaKaW RaKjKjW RbCcF RVJYP R[D\\I RZM]M]WcZ RiDeHdK R]WYY RVHRU RVFTQ',
	    'Po[GkG RRI[I R\\JkJ RZMlM RZSlS R\\[_[ RWCW[ R_O_[ RcGcM RWNZQ RiOkQ R^D`E RgCfF RUPRU RjUgYcZ RWHTT RlYl[i[ RbO[O ReNgY RcVZW',
	    'Po[EfE RRJYJ R[MeM R\\PdP R[SeS Ri[l[ R][`[ RVCV[ R[H[M R_E_M R`S`[ RbEbM R[HeHeM RhGhT RlCl[ RcVeY RVKXP R\\V[Y RVHRU',
	    'Po[FlF RRI[I RWCW[ R`D`H RgDgH RbTeV RiUlX RbL\\Q RfMaR R_I[M RVIXMZP R^MgQ R]U[Z RfJdP R`U`[hZh[ RWHST RiRfS R^KjJiS',
	    'Po\\GkG RRI[I R]JjJ R[MlM RWCW[ RcGcM RfWj[ R[P_R R_CaG R^VZY ReWa[ RbQkQjT RgCfG RWIYN RWJRT RbO`T RWGTS RfQeX',
	    'PoZFlF RRIZI R_MgM R\\PjP R]RiR RZUlU R^[c[ RVCV[ R\\H\\P RcUc[ R_K_LgKgM R\\HjHjP RgWkY RcCcF RVIYP R]XYZ RVIRU RVFTQ',
	    'PoRI[I R`KgK R`OgO R[SlS RWCW[ R`F`O RcOc[ RcCaFgFgO RcRjY RYNZQ R[E^H RhLlO R^K[O RkEhH RcS^XZZ RWHST',
	    'PoRI[I R`PgP R\\TjT R`[c[ RWCW[ RcCcF RcTc[ R\\F\\GkFkH RgVkZ R[M_O RWL[P RfMkR R]KaM\\R R_V[Z R^I[M RdHgO RfJjJhO RVLRU R^JbJ`O RWHTS',
	    'Po\\FlF RRI[I R_LiL R\\OlO RWCW[ R\\I\\M RaCaH RfVfZ RgCgH R]IkIkM RYNZR RgUlZ RaQdTfX ReOhW RdO\\S RcS[W RkRgT RcW[[ Rd[a[ RWHST',
	    'PoSJXJ RdJkJ R[ObO RdOkO RVCV[ R[R\\W R\\J`J^Y RaR`V RdEdZ RdEkEkO R^DbH RkRhU RUKXQ RgRjZ R^C\\I RgYcZ RfOiX RbXZY RUHSU',
	    'Po[EkE R]HjH RRJZJ R^MiM R\\PjP R[RlR RcUlU R\\YaY Rg[i[ RWCW[ R\\T\\Z R\\TaTaY RcMcO RcCcH RjSj[ R[KkKkM RdWeY RVLZP RWHSU',
	    'Po[ElE R\\HjH RRJ[J R]MiM R]RjR R\\WjW RZ[l[ RWCW[ R[O[R R]J]M RcCcH RdUd[ R]JiJiM R[OlOlR R[UiTkU RaR^U RVJYP RWHST',
	    'PodJkJ RdNkN R[ObO RjYlY RWCW[ R_O_[ RdQdY RdFdM RhNh[ RdFkFkM RdQlQlY RSIZI^K RVJYQ R`LbP R^C\\I RaF]N RgCgF RaRaW RVHSU R\\SZY',
	    'Po]GcG ReGlG RSIZI R[LkL R_OhO R_ShS R[VlV RWCW[ R_I_V RhIhV RgFiH RWKYP RfXkZ R^C[H RfCcI RaX[[ RWHST',
	    'Po^HjH RRJZJ R^LiL R^NiN R^SjS RdXgX R\\ZlZ RWCW[ R\\D\\[ R^J^N RdFdQ RgRgX R^JiJiN R\\DlDl[ R`UbW RVKYP RVISU',
	    'Po^IiI RRI[I R^RjR R^TjT R^WjW RWCW[ R\\F\\I R_O_W RdCdF R[OjOjW R\\FlFlI RWMZP RgYl[ R`K\\L RbY[[ RdIdM RWHST RhK\\O',
	    'Po[FlF RSI[I R]KkK R^MjM R^OjO R^RlR R\\UjU RWCW[ R^I^R R`C`G RdIdR RgCgH R^J[M RXMZQ RUORU R_WeYl[ RWHTS RhWcY\\[',
	    'PoRIZI R\\NlN R^XcX R\\ZlZ RVCV[ R\\D\\Z RiHeL RbS^X RiRhUeX RVIYP RbH^LcKcM RgOeT R`D_H R`P^T RgDeH RVHRU RbH^H RiGeH RbS^T RiSeS RVFTS RjJjLeL RjWeX',
	    'Po[FkF RRJ[J R\\LjL RaRhR R_XcX RfXjX R]ZkZ RWCW[ R^K^[ R_T_X RaNaQ R_TcTcX RfTfX RaNhNhQ RfTjTjX ReFkL RaF[M RVKYP RbC_J RWHSU',
	    'Po[DlD RRIYI Rh[k[ RVCV[ R]I][ RcDc[ R[KkJk[ R^TbW ReMiQ RgFlJ R^MaO]Q RaS_X RhNeQ RhSgW ReThWeY R_F[J RVJYP RVHRU RVFTS',
	    'PoRIZI RaMgM R[SlS R[VlV R`[d[ RWCW[ R]O]S RaIaL RbObS RdCdF RdVd[ RfOfS R]OjOjS R\\F\\GkFkH RXMZP RiIlL R^J[L RbHeI RWHST',
	    'PocGlG RZG`G RRJXJ RcJjJ RbXgX Rf[j[ RVCV[ RbSbY RbSgSgX RaPkPj[ RWKYP R\\C\\F RfKkM RdCaJ RfHdMaN R^Z[Z RVGST R[G[PY[ R[M_M_V^Z',
	    'Po\\FlF RRIZI R^KiK R^PiP RWCW[ R[M[P R^H^K RfVfZ R^HiHiK R[MkMkP RbRfUlY RcCeE RWJYQ RdSfX RbQ]T RdS\\W RiSfTeV[Z RWHST Re[b[',
	    'Po\\DcD RgHlH RRJZJ R\\LcL ReOlO R\\PcP RZZlZ RWCW[ R\\D\\P R]S]Z R`D`H RaSaZ R\\HcHcL RfSfZ R]SjSjZ RXNZR R`L`P RWHSU RgDfK',
	    'Po]IiI RRJZJ R\\LkL R[QlQ R]TiT RWCW[ RcIcL R]NiNiT RXNZR RhVlZ RhEkI R^EcEcH RaUeV R`V`[gZh[ R]E\\I R\\V[Z RWHSU RiCaD',
	    'Po]EkE RSIZI R^JbJ RfJjJ R^MbM RfMjM R\\PlP RWCW[ R]S][ RbSb[ RdEdN RfSf[ R\\H\\IlHlJ RWM[Q RUPSU RdPcS RWHTT R]SkSk[i[',
	    'PoTQ^Q R`QjQ RSLSO RYNY[ R\\S\\Y RdNd[ RSLkLkO RiShZlYlZ RkEhG RUESG R]E[G RaE`H RXH]J ReHlK RdI_K RWIRK RYCXI ReCdI RUSTY RaS`Z',
	    'PoUEkE RaJkJ RRUlU RWLWS R_S_[ RWL`L`R RgNkR RhGjH RfJiQ RaVjZ RcQaR R]VS[ R[H_H R\\P`P RWFXIYI[IWH RWF_F_J[J RWP[PWP RWN_O[P R]R_R]R RUEUKSR RfFdP',
	    'Po[FjF R^HhH RRIYI R^KhK RZNlN R\\UjU R_[c[ RVCV[ RcVc[ RhChM RgSjV RVJXP RgOlQ RfXjZ R^C^M]PYQ RcP^ReR\\U R^XZY RVIRU RVFTR',
	    'PoRIZI R]VjV R\\ZkZ RWCW[ R\\F\\H R]Q]V RdCcF RdOdZ R]QjQjV R\\FkFkI RhWl[ RXNZQ RiJlM R]J\\M RaHdI RhHdL\\P RWHST RaJaOhNhO',
	    'PoRI[I R]QkQ R^SkS R^WkW R^ZlZ RWCW[ R^O^[ R\\DbDbM RdQdZ ReDkDkM R_NZS RhJdK R`H]L RYNZQ RhFdG R_F[G RWHST ReMdQ',
	    'Po\\DlD RRJZJ R`JjJ R^LlL R`QjQ R`SjS R`UjU R\\ZlZ RWCW[ R\\D\\[ R`N`U R`G`J ReFeL R`GjGjJ R`NjNjU RXNYQ RgWkX RcW^X RWHSU',
	    'PoZGlG RRIYI R\\PjP R\\RkR RZVlV RVCV[ R\\J\\P R`W`[ R[EcEdU R\\JjJjP RhWl[ RdWf[ R\\XY[ RVJYQ R_L`N RgLfN RVHRU RVEUQ RiCbD',
	    'Po[FlF RRIYI R]WiW RVCV[ RdJdO RcCcF RfJfO R^RjQj[ RfGcJ RVJYQ RiIkO R]J\\O RiNeO R[ZZ[ RVHRU RkIfI R[J`J`O^O RVFUQ R^P]U]Y',
	    'PoRIYI R[KdK R[PdP R`[k[ RWCW[ R[F[P R_F`PaZ R[FdFdP RWMZQ ReMhN RfHiI RjCkXl[ R_C^F RdUdXaW RVHSU R_M`T R]P[Y RlQdR',
	    'PocEkE RRJYJ RdMkM R[NbN RdRkR RdVkV RVCV[ R_C_N R`TbX RdIdV RhEfI RdIkIkV R\\Q`U RiXlZ R_V[Z ReXb[ RVJYP RbO^Y RVIRV RVGTT R[H[SY[ R[HbHbL',
	    'PoRIZI R^NhN R\\RkR RWCW[ R^K^N R^KhKhN R_TgTg[kZl[ RXNZR ReEjI RkFgG R^X\\[ RiDeE R\\F`HgHlJ RfOfQ R\\EaE]IZJ RWHSU R_T^Y R`P`R',
	    'Po\\ElE R]IeI RRIZI ReLlL R^MdM R^TdT RWCW[ R^O^S R_U_X RaGaM R^OdOdS RkGk[ RVLZP RgPgU RcUbX RWISU Rg[j[g[ ReY]Z R\\E\\RZZ',
	    'Po\\HbH RfHlH RSJYJ RfLlL RaYgY Rh[l[ RVCV[ R\\E\\[ RaUaZ R\\EbEbL RfEfL RaUgUgY RfElEl[ R\\LbL_Q RVKYQ RbOeSiT RbOgOdS_U RVGST',
	    'Po]HjH RSJZJ R]JjJ R]LjL R[TlT Rd[i[ RWCW[ R]E]L R]EjEjL RcRdT RVJYO RfQlR RaWjWi[ RaP\\R RaV^YZ[ RWISU R[OlOlQ',
	    'Po\\DkD RSI[I R^IiI R^LiL RaSgS R_VhV RWCW[ R\\N\\Q RcEcG R^GiGiN R^G]NkNkQ RhQkS R^Q\\S RWJYP RbOeP RaQaS RaU[W R`WeYk[ RWHST ReY[[',
	    'PoeHlH RRIZI R^QjQ R^TjT R^WjW RWCW[ R^M^W R]GbGaL RiGiL R^MjMjW RVJYO RgYl[ RaY[[ RVHST RbD]D\\L RkDeEeL',
	    'Po]FlF RRIZI R_JkJ R]MlM R_TjT R_WjW RWCW[ R_P_W RbGbM ReMeW RgGgM R_PjPjW RcCeF RWJZP RgXl[ RbX][ RWHSS R]F]Q[Y',
	    'Po^DlD RRIYI R_KkK R_NlN RaTiT Ra[l[ RVCV[ R_H_K RcDcK RgDgK R`RiQiT R_HkHkK RbPaXjWjX RZE[H RgNkR RdM_R RZM]M\\XY[ RVJXP R]Xb[ RVGSS R\\MZM',
	    'PoWGgG RWIgI RSN]N RaNkN RSQ]Q RaQkQ RRTlT RSLSQ RWDWI RXLXQ RSL]L]Q R_D_I R_R_[ RaLaQ RfLfQ RWDgDgI RaLkLkQ RaVkZ R]VR[',
	    'Po^IjI RRJ[J R^MjM R\\UcU ReUlU R\\ZcZ ReZlZ RWCW[ R\\P\\[ R^D^M R_P_Z R\\PcPc[ RdDdM RePe[ RiPiZ R^DjDjM RePlPl[ RWMZP RWHST',
	    'Po\\FlF RRIZI ReJkJ RaPgP R]TjT RWCW[ RaCaH RgCgH R\\O_O RVJXO RhWlZ RfNkS ReIhP R^LaM\\S R_V\\Z RjKgN R^J[M R^JbJ`O RWHSU RdTd[`[',
	    'PoRJ[J R^LkL R^WjW R^[j[ RWCW[ R^T^[ ReIeO R^TjTj[ RWMZP RePfR R^P\\S RaD\\H R`EfEdH RbPaS RWHST R^IkIkO^O^I RjPmS',
	    'Po\\ElE R]HlH RRIZI R_NjN R_QjQ R`XiX Ri[l[ RWCW[ R^T^[ R_K_Q RbFbJ ReKeX RhFhJ R_KjKjQ R^TlTl[ RVKYP RhViY RWHSU R\\E\\SZZ',
	    'Po\\EbE RdEkE R[IbI RdIlI RSI[I R]QiQ R]TiT R]WiW RWCW[ R]N]W R_JbL R]NiNiW RWMZP RhJlM RgHiK RfXl[ R]L[M ReLcM R`YZ[ RWHST R_D^K RgEgK',
	    'PocEjE RSJZJ RaJhJ R_KfK RcMkM R_RjR R_TjT Rb[h[ RWCW[ R_O_T RcIcM RcCcG RdOdT R_OjOjT R\\HkHkJ RcVfW RiWlZ R^W][ RVKZR RaXb[ RWHSU R\\H\\S[Z',
	    'Po\\FlF RRI[I R]NkN R]QlQ RWCW[ R]I]N RaCaH RbIbN RfIfN RgCgH R]IkIkN RlXl[ RfWi[ RWM[Q RgOjP R^UaV RiSeX`Z RWHST RdNeX R\\Q[Z',
	    'Po[EbE RdElE R\\HbH ReHkH RRIYI R[KbK RdKlK R]QjQ R]TjT RVCV[ R_C_L RhChL R`V_[hZh[ R]NjNjT RcUeX RhVlY RVJYQ R\\VZZ RVHRU',
	    'Po[FlF RRIZI R\\JkJ RaPiP R_XdX RfXkX R\\ZlZ RWCW[ R\\J\\Z R_S_X R`D`H RaLaP R_SdSdX RfSfX RgDgH RaLiLiP RfSkSkX RWJYQ RWHST',
	    'Po\\HcH RfHlH RRI[I R\\KcK RfKlK R`RhR R`YhY Rh[l[ RWCW[ R\\D\\[ R`U`Z R`N`R R\\DcDcK RcRcU RfDfK R`NhNhR R`UhUhY RfDlDl[ RWMZP RWHST',
	    'Po\\GcG ReGlG RRJYJ R\\ObO R\\RbR R\\UbU RhWkW R\\XcX RWCW[ R\\K\\[ R\\KbKbR ReKe[ ReKkKlW RfFiI RVKYQ R]C[I RfCcI RVHSU',
	    'PoSEkE RVIhI RbNhN R[PkP RWUgU RRWlW RWRWU R_U_[ R_C_K RbLbP RWRgRgU RVGVHhGhI RSKSLkKkN R[MUN R_VdYl[ RXN^NZPTQ R_VZYR[ RYOaP',
	    'Po[ElE R]HkH RRI[I ReLkL R\\NkN R]SjS R]VjV RWCW[ R\\J\\N RdCdH ReJfL R]QjQjX R\\JkJkN RWMZP RWKRU RaL^M R]Q]XhYl[ RWHTT RaYZ[',
	    'PocIjI RbLlL RZPaP RVCV[ R^O^[ RRJZI]K RgLcP RfQbT RlOiR RgTbY RVKYQ R`SaV R_LaP R]C[H R_G\\N RVIRV R[SZX RdCcJ Rg[d[ RhLiV RdFjEiL RVFTQ RgPgWg[',
	    'PoZSlS RWCW[ RXNZR RcShXlZ RcS^XZZ RWHSU ReK`K RSJYJ R]D[I]J R_G[P^O R^N_P RiDgIiJ RkGgPjO RjNkP RaFeFePaPaF RcPc[ RdDaF',
	    'Po\\FlF RSIZI R^RbR RiQkQ RdTkT RWCW[ R_NbNbQ ReVj[ RcCdF RkJiL RjVe[ R^HcI_L RVJYQ RhHfIhLlM R^JaK]M RfNiNiR RVHSU ReNeR Ra[^[ R^Q^UbUa[ R\\F[SZ[',
	    'Po]GcG ReGlG RRIZI R]KhK R[OlO R_ViV R_YiY RWCW[ R_R_[ RbIbO R^SiRi[ R_F`I RVJYQ RiFhI R^C[I RfCdH RjJfO[T RWHSU',
	    'PoZHdH RRIYI R[KcK R[QcQ RVCV[ R[N[Q R[E[K R[EcEcK R[NcNcQ RVNYQ RgVc[ RgRk[ R\\WbY RjPfY R_C_P[W RZTcTaW_YZ[ RfMiX RVISU RfIhW RfIkIhU RfCdN',
	    'Po[FkF RSJYJ R_KkK RaQjQ RaTjT R_VkV RaZlZ RWCW[ R`C`H RaMaT ReIeY RfCfH RaMjMjT R[P^P^WZZ RVJXO R[H]M R]XcZ RWHST',
	    'Po\\GcG ReGlG RRJYJ RZLlL R_[b[ Rg[k[ RWCW[ R\\O\\[ R\\ObOb[ RfPfW RkOk[ R^U`V R^J`L RfFiI RhJeL RVKYQ R]RaS R]C[I RfCcI RVHSU',
	    'Po\\FlF R^IeI RRJZJ R^LdL RfMlM R^SdS RWCW[ R^O^S R_U_X RaGaL R^OdOdS RVKYQ RdCdF RgPgU RWHSU RcUbY RjGj[g[ ReX]Y R\\F\\RZZ',
	    'Po\\ElE RRIZI R\\KkK RaRgR RVCV[ RaOaR RaFaK RfFfK R\\HkHkK RiPkR R^P]R RaT^W RVIXN RcOfP R\\NlNkP R_VeYl[ RVHSS R_ViVcY\\[ R\\E\\QZY',
	    'Po[EaE RcGiG RZHaH RRIZI R[LaL RbLlL R[PaP RdUkU R[ZaZ RdZkZ RWCW[ R[S[[ R[SaSaZ RdPd[ RcQkQk[ RWMYP RhNjP RfCgLbR RkDfN RWHSU',
	    'PodEjE RRI[I R_KeK RcMjM R_QjQ R_TjT R\\[l[ RWCW[ R^V^[ R_O_T RcVc[ RcHcM ReOeT RfVg[ R_OjOjT R\\HlHlJ RXNZR R^VjVk[ RWHSU RcEcK R\\H\\R[Y RgJbJ',
	    'Po\\HbH RfHlH RRIYI R\\KbK RfKlK R`YhY RVCV[ R\\D\\[ R`S`Z R\\DbDbK RfDfK ReRhRhY RVJXO RbM_Q RbNgNfQ RcR`R RVHST ReVhUeU RfDlDl[i[ RcV`U',
	    'Po\\DkD RRJZJ ReKlK ReUkU R\\ZlZ RWCW[ R`T`Z RaGaQ ReReZ R]KbKcN RVKYQ RiGhMkO RhJfO RaJ^P RWISU RcG]G RkGeH R\\D\\RZZ',
	    'PoSD^D R_FgF R^IlI RaNjN RaQjQ RSTlT R_Q_[ RaKaQ RcCcI RaKjKjQ RWNSP R\\GZJUO RYJ]L RaVlZ RjDfI\\N R]VR[ RYDVHRI RXIVKRL RZMZRVR RZIZP',
	    'PoZGbG RRIZI RdIkI RZMbM RdNkN RZRbR Ri[l[ RWCW[ R\\H\\L R^C^G R^N^[ R`H`L RdEdSjSi[ RdEkEkS R^SbW RWMYP R\\UYY R^Q[W RlXl[ RVHSU RfSdZ',
	    'PodDkD R[GaG RRIYI RcJkJ R[JaJ R]TiT R]WiW RZZlZ RVCV[ R[D[O R]R]W R[DaDaJ RdGcJ R]RiRiW RVJXO RgLcP RiLhOlNlO RVHRU RaM[O RVFTS RhEfM',
	    'Po[ElE RRJ[J R\\MaM RfMjM R[OkO R\\WaW RfWjW R[ZlZ RWCW[ R\\H\\M R\\R\\W R\\HaHaM R\\RaRaW RcEcZ RfHfM RfRfW RfHjHjM RfRjRjW RXNZR RWHSU',
	    'PodKkK R[ObO R[TbT RVCV[ R[I[S R]V]Z R_J_S R_V`Y RRJZJbJbS RhDh[ RVJYQ RbVbY RhKcV R]CZK R\\FaF^K RgJkV RVIRV R[VZ[ RjDdE RVGTT RgHeR',
	    'Po[FkF RSIZI R\\MjM R\\OaO ReOkO R[RaR ReRkR RWCW[ R\\H\\M R_U_Z RaHaM RcCcF RcNcR RfHfM R\\HjHjM RXNZQ RdUhYl[ RcS[W RkTeW RWHST ReY\\Z',
	    'Po_FkF R_JlJ RUL]L R_NkN RUR^R RRTlT RUIUQ RSJ]J\\L RTDTF]E]F RUN^O^R_[ ReCeF ReJeR RbGbK RaVjZ RiGhI R]VR[ RXDWHRJ',
	    'PoYE`E RRIZI RaNkN RY[\\[ RVCV[ R\\M\\[ RbHbN R`JkIkN RVNYQ RZH^K RkPhS R_F\\I RdE_J RgPjX RYM_M^R RfMcP_R ReRbT_U RfTcX_Y RcFhFfKiV RVIRV Rf[c[ RfSgY RfIgR',
	    'PoeFlF R[FcF RRIZI RZLcL ReNkN ReQkQ R[SbS ReTjT R[VbV ReWkW R_[b[ RWCW[ R[O[[ R\\H\\K R_C_G R[ObOb[ ReCeIkIkM ReNe[lZl[ RWMYP RaH`K RWHSU',
	    'PoZGbG RRIYI RdNlN R[QbQ RdRlR RdVlV RVCV[ R[K[Q R^D^[ R[KbKbQ RdJdV RdJlJlV R^SbV RiXl[ RfXc[ RVJXP RfFkFiJ R^P[X RfCdJ RVIRU',
	    'Po[ElE RRIZI R]KkK R^NjN R^QjQ R^TkT RWCW[ R^J^U R`C`G R`Xa[ ReIeT RhChG RjWk[ R^J[N RWJYQ ReWf[ R\\W[[ RWHSS',
	    'Po^IiI RRJZJ R^KjK R\\NlN R]TkT RWCW[ R\\F\\H R]P]T RaPaT RdCdF RdGdN RaVa[hZh[ R]PkPkT R\\FlFlH RcUeW RYMZP RiWlZ R]W\\Z RfPfT RWHST',
	    'PoRIYI RdJjJ RZOaO RZQ^Q RdVkV RZVaV RZX^X Rb[l[ RVCV[ R[L^L^Q RZS^S^X R\\F\\JaJbZ RdJd[ R\\FjFjJ RdMkMkV RfPiT RiPfT RbD`F RVGST',
	    'PoaEeE RfHjH RRIZI RfKjK RZNlN R^RgR R]UiU R[XjX RWCW[ R\\E\\N RaJaN RbPb[ RfDjDjN RXNYQ RaCaHeHeNlT R_NZT R_D\\E RWHSU R_H\\H R\\K_K\\K',
	    'Po[ElE RRJYJ R[KlK R]NjN RfSjS RVCV[ R`C`G RaP`S R]P\\XaWa[ RcGc[ RfPgS RfWfZ RgCgG R]IjIjN RjPj[ RVKYQ RVIRV R]UjVfW R]RaR R]R\\Z',
	    'Po\\EkE RRJZJ R^KbK RfKjK R]NbN RfNkN RbZfZ RWCW[ R\\H\\K R`Q`[ RbQb[ RdEdO RbQfQf[ RhQh[ R\\HlHlK RWKYP R\\Q\\Z`Z RWISU R\\Q_Q\\Q RhQlQlZhZ',
	    'Po\\GbG ReGkG RRIZI R\\JbJ ReJkJ R\\MbM R[SlS RWCW[ R\\D\\M R\\DbDbM ReDkDkM RVLZO ReDeMjMlQ RfNdQ R^N[Q R`R^WlZ RWHST RhUdYZ[',
	    'PoSIYI RZLlL RaOdO RVCV[ R^L^Y RaLaY R]F_H RbFeI RVKYQ RiEkH RlXj[k[ R]D[J RcD`J RVHSU R[O^OZO R[R^RZR RdRaR RZU^UZU ReUaU RgCgPhZ RjOgWdYZZ',
	    'Po^DeD R]GhG R\\IkI R[MlM R]OhO R_SgS RRVlV RTDTS R_T_[ R`P`S R]KiKiO RWJZL RiPkS RhEjG R\\QZS R^D[G RbPeQ RTEZDWI R_UeYlZ RYOWO R_UXYRZ RgCaD',
	    'PoZObO RYSbS RZZaZ Rd[h[ RVCV[ RZUZZ R^N^Z RaUa[ RhPh[ RjHhKcQ R[LYP RfHcK RVIYN RRIZHZK R]G^J RaG`K RgEeI RjTkY RVHRU RdTdY RaEZF RkDcE RVFTQ RhKcL RjMkQcQ',
	    'PoRIYI R^SiS R]ZjZ RVCV[ R[L[P R]U][ R^O^S R^OiOiS R]UjUj[ R[LlLlP R_GbJ RhGkJ RlEjG RcEaG RVJYQ R\\E\\G ReEeG R^J[J RfJdJ RVHRU R_C^I RhCgI',
	    'Po[FlF RRIZI ReLjL R\\NkN R]QjQ R[TlT RVCV[ R\\H\\LaL`T R\\HbHbL ReHfLfT ReHjHjL RVJYQ RcCcF RdUgYl[ R_W_[ RdTZX RjVfW RVHRU RdY^Z',
	    'PoZHcH ReIlI RRJYJ RZLcL RZNdN R\\QcQ RZTdT RVCV[ RZEZL R]T]Z R^C^L RaCaL RZEcEdL RiCi[ R_VcZ RdVaW RVJYQ R_Y\\Z RVIRU RiGlU RiGfU RZNZUY[',
	    'PoZElE RRIZI R_JlJ R`OkO R`QkQ R^UlU Re[h[ RWCW[ R`L`S R`C`G ReHeS RfCfG RhTh[ R`LkLkS R[M]P RaWcY R[I]K RVJYQ RhHjH R]SZZ RWHSU',
	    'PoRI[I R]JjJ R^LcL ReOkO R]VjV Rd[l[ RWCW[ R^R^V RdJeO R\\HjGjJ R\\SjSiW RgXiY R`O[T R^EfEdG RUPRU RWM[P`PfQcSbW`Y[[ RdWd[ RWHTT RjLdM R`C\\G^O',
	    'Po\\HcH RfHlH RRI[I R\\KcK RfKlK R_NjN R`SiS R`UiU RWCW[ R\\D\\[ R`P`U R\\DcDcK RdMdZ RfDfK R`PiPiU RfDlDl[ RdUiY RWMZP RdU`Y RWHST Ri[l[i[',
	    'PobEeE R\\H`H RgHjH RRIZI RgKjK R\\K`K RZNlN R]TiT R[WkW R_[c[ RWCW[ R_D\\E]N RbJbN RcQc[ RgDjDjN RXNZR RbCbHeHfNlT R`NZT RWHSU RfQ_R',
	    'Po\\GcG RdGkG RSIYI R\\JbJ ReJkJ R\\MbM ReMkM R\\PcP RePlP RWCW[ R]E]P R`E`P ReEeP RiEiP RVJYO R]C[J ReDeJ R_TbXgZl[ RVHSU R[SjSgWbY[[',
	    'Po[DkD R^GhG RRIZI R^JhJ R[MjM R[RaR ReRkR R[UaU ReUkU RVCV[ R[O[Y R^D^L RZObOa[ ReOeY RhDhN RdOkOk[ RVJYQ RVIRV RaXYY RlXeY',
	    'Po]GkG RSIZI R^LkL R^OkO R^QlQ RaZgZ Rh[l[ RWCW[ R\\S\\[ R]D]G R^J^R RaWa[ RdCdG ReIeQ RaWgWgZ RkDkG R\\SlSl[ RWJYP RbU^V R_H[L RfTjV RWGST',
	    'PoZGkG RSIZI R[KkK R\\NjN RZPlP R\\SbS R\\VbV RWCW[ R\\P\\Y RaCaG RbPb[ RcKcP ReCeG R_H`K R\\D^F RiDgF RfHeK RiVe[ RVIYO ReUhYl[ RcSjShX RVHSU RdSdU RaXZY',
	    'Po[FlF RRI[I R]MbM ReMkM R]RkR R^TjT R^WjW R^ZlZ RWCW[ R]I]M R^P^[ R`C`G R]IbIbM ReOeZ ReIeM RgCgG ReIkIkM RWMZP R_O[T RWHST',
	    'Po\\GbG ReGkG RRI[I R\\JbJ ReJkJ R\\LbL ReLkL R]QkQ R]SkS R]VkV R]ZlZ RWCW[ R\\D\\L R^O^[ R\\DbDbL ReNeZ ReDeL ReDkDkL RWK[P R^OZS R^M]R RWKRU RWHTS',
	    'Po[FlF R^KlK R[O^P R`XfX RVCV[ R[K[P R^K^X R`C`H R`N`X R`NdNcQ R`QfQfU RlXl[ RiHlI RVJXP RjQfZ R`UdUcX RkNhW RRIWIRU R[S[Z RfCgIhRiZ RVFTQ RZS^S][',
	    'Po[FlF RSIZI R]JkJ R]LjL R]OjO R]QkQ R\\WbW ReWkW R\\ZkZ RWCW[ R\\T\\[ R^I^R R`C`G ReIeQ RgCgG ReTkTk[ R^I[L RWJYO RWJRT RWHTS RbS\\T',
	    'PoRF[F RcFlF R`RkR RVS^S Rd[h[ RVPV[ RWCWL RVP^P^V RgCgL RhOh[ RSMSOkMkP RWH[I RbUeW RVV]W_[ RgFlJ RfHcK RWFRK R`E\\F R`H]JaJ R]DaF RgDeI R^YUZ',
	    'PoRIZI R[IkI RdOlO RdRkR RZTbT RdVkV RdZlZ RVCV[ R[E[I R^T^[ R`EaJ RdMd[ RfEfI RiMiZ R[EkEkI R`QaT RVLZP R`M^Q R[N^Q[T R]K\\O RaVaY R[VZZ ReKbQ RVISU RiKhR',
	    'Po[DbD ReDlD RRIZI R[MlM R[SkS R^VcV RWCW[ R\\G\\K R^S^Z R_H_J RaMaS R\\GbGcJdL ReGeK RhHhJ ReGkGkK R[PkPkS RfMfSf[lZl[ RVKYP RWHST RkVeW R[M[TZ[ RcZ\\[',
	    'Po\\FbF RdFkF RdIlI RRIZI RiMlM R]QjQ R]TjT R]WjW RWCW[ R]N]W R]NjNjW RXNZR R\\D[G RfYl[ R_C^I\\M RdDdG RhCgIdM RbL_L R`YZ[ R[IaI`M RiIiM RWHST',
	    'Po\\DbD R[FcF R\\IbI R\\KbK RRLZL RcLlL R\\QbQ RRTlT RVKVR R\\M\\R R_R_[ R\\MbMbQ RhKhR RdFgI RiIlL RjFdK RUCSGVHSK RgCeG RYMYP RjMlQ RaVkZ RSMRR ReMdQ R]VS[',
	    'PoRJZJ R^KiK R\\QkQ R\\TkT RZWlW RWCW[ R[G[J R\\N\\Q R^I^L RaYa[ RcCcG RcNcW R^IiIiL R\\NkNkQ R[GlGlJ RiXl[ RXNYR R^D^F RhDgF R\\YZ[ ReYf[ RVHSU R`O`Q',
	    'Po\\HbH RdHjH RRIYI R\\JbJ RdJjJ R]OjO R]QjQ R]SkS R\\VjV RVCV[ R\\D\\J R^L^T R\\DbDbJ RdLdS RdDdJ RdDkDkJ R^LZP RVJYQ R\\F`F RVHRU RbF^F RhFdFhFkFgF R]VcYl[ RiWdY[[ RVETQ',
	    'Po\\HbH ReHkH RRIYI R[KlK R^ScS R^VcV RVCV[ R^P^Y R]MbMbP R[PdPc[ ReDkDkH RhWl[ RhWe[ RVJYP RfQjY RjRgZ RfOkOhX RVHRU R\\D\\H R\\DbDbH ReDeH RfLeT RVFTS R[K[SZZ RdX]Y',
	    'Po[DbD ReGkG RRIZI R[JbJ R[MbM RdMlM R]RjR R]TjT R]WjW RWCW[ R[D[M R]O]V R_D_G R_J_M R[GbGbJ RdJdM RfJfM RiJiM R]OjOjV RdJlJlM Re[lZl[ RXNZR ReCcH ReWe[ RWHSU R`W_YZ[',
	    'PoZElE RRIZI R[KaK ReKkK R[NaN ReNkN R^PhP R_VhV RWCW[ R[I[[ R_R_V R_C_G R[IaIaN RcOcZ ReIeN RgCgG R_RhRhV RcUgY RWMYP RcU_Y RWHSU ReIkIk[h[ RaSaV ReSeV',
	    'Po\\GlG RRIYI RgKkK R_PkP R_WhW R^ZiZ RVCV[ R_M_P R_T_W RdRdY ReHeL RgMgP R_ThThW R_MkMkP R\\DlDlG R_SlRlZ RbMbP]U RVJYQ RkIfI Rk[i[ RVHRU R_IcJ^K R\\D\\RZZ',
	    'Po]FjF R]IjI RSI[I R\\McM ReMlM RePlP R]VkV R`[d[ RWCW[ R]D]I R_K_O R\\KcKcP RdDdI RdUd[ RhKhO R]DjDjI ReKlKlO RiXk[ RWM[Q RiTlW R_X\\[ R\\K\\PcP]SfS[U RhQ`U ReLeP RWHSU',
	    'Po^GgG RRIYI R[IlI R[WbW RdWkW RjXk[ Rd[l[ RZ[b[ RVCV[ R[T[W R^R^Z R[TbTbW RdTdW RgRgZ RdTkTkW R_C^G RUIYP RbJeMhPlQ R_EhDfI RdI]L RcL\\O RiKdMeO\\R RdRaR RVGST',
	    'Po[EkE RSIYI R]JaJ ReJjJ R\\LaL ReLjL R\\QbQ ReRlR ReVlV R[VcV RYXcX Rh[l[ RVCV[ R[G[J R[S[V R\\N\\Q R_Q_[ RZOcNbQ RcEcL R[ScScV ReNlNl[ R[GlGlJ RVJYP RVISU ReNeUd[',
	    'PoZEkE RRIYI R\\IiI RdOiO R[QjQ R\\SjS RZUlU RVCV[ R^W^[ R\\M]O`O_U R\\MaMbO RcDcK RdMeOfU RdMiMiO R\\G\\HiGiI RZKZLkKkM RVJYQ RcUfYk[ RjWfX RVHRU RdUYX RcY]Z RVETQ',
	    'PoRF[F RcFlF RTPjP R_TlT RUT]T RUV]V RTZ]Z RRZ[Z Rd[h[ RTLTP RURUZ RWCWK R[L[P RUR]R]V RbLbP RgCgK RhQh[ RTLjLjP RaVdX R[X^[ RgElJ RXFRK R]D`E\\F R`I\\K RfDeHbJ R]HaJ RWG[I',
	    'Po\\EkE RRIZI R]JaJ ReJiJ R]LaL ReLjL R[TkT RZ[l[ RWCW[ R\\N\\R RbNbR RcEcL RcTcZ R[G[IlGlJ RhVkY RVLYO R_VaY R_V\\Y RhVeY RWHSU R\\N_N_R\\Q RbNeNeRaQ RgNgRkR RgNkNkRgR',
	    'PoZEdE ReIkI RRJYJ R[M^M R`McM ReNkN R[QcQ R[ScS R[VcV R[YcY Ri[l[ RVCV[ R[I[M R\\P\\Z R]C]G R[I^I^M R`O`Y RaCaG R`IdIcM ReDeRjRj[ ReDkDkR RVIYP R\\NZS RlXl[ RVIRU R`I`M RcI`I RfReY RVFTQ',
	    'Po_PbUfXl[ RWHkHhO RYDVKRO R_J_PYXR[',
	    'PoTFXI RXQRW R\\IkIiO R^DYN RcIcO`V\\YX[ RcPeVj[',
	    'PogVl[ RSJVN RbU\\[ RXSRY RXO[W RdOhY R[LWV RdPaX RSG\\GYS RaC_M RaHkHjL RdLcU',
	    'PoTL^L R[L[Z RaHkHjN RbD_N R]DTETSSZ RfIeRcW^[ ReRhWl[',
	    'PoWH^H RW[[[ RWCWM RgWl[ RcV][ RfHfQiY RdQaX RaIkHjO RbD_N ReNcV RZRRT RRM]M\\T[[ ReKfQdT',
	    'PoRMYM RRVYV RRERX RREYEYV RfUk[ RaU[[ RcOgX R^HlHjO RcO`W R_C\\N RcLeT RcMbU',
	    'PogVl[ RWJYM RXD^J RcV_[ RWFRK RePiY ReRaY RTP]PXZ RbHlHjN RcC`M ReMgV ReLdV',
	    'PoRE^E RRX^X RRERZ RTJ]S R[IVT R`HkHjN RaC^N ReIdRbV^[ ReRgVl[',
	    'PoUTZT RY[\\[ RULUU RULZLZS RhWl[ RcV^[ RfHfRiY ReQaY RWCRM RbHkHjO RcD`N RUH^G^T]Z ReLfQdU',
	    'PoYN]N RTY^Y RTQT[ RTQ^Q^Y RSE^E^M RhVl[ RcW_[ RWKRO RePiY RePaY RbHkHjN RXEVL RcC`N ReKgU ReLdV',
	    'PoRM\\M RVY]Y RRYXY RRQRY RRFRM RWDWX R\\F\\M R]Q]Y RhWlZ RcV_[ RePjY RfPbY RaIlHjN RbC`M ReLfQdV',
	    'PoTH^H RRM_M RYR^R RTPTY RYGYX RgVl[ RcV^[ RfHfRiY RePbX RaIkHjN RUCSK RbD_N ReMgV R_VRY ReLdU',
	    'PoRG`G RhWl[ RSLXP R[U^Y RcW_[ RWITM R[MST ReHfRiY ReRaY RYCYGWJ R]RWYR[ RbIkHjN RcC`N ReLgV ReLdU',
	    'PoTL\\L RRY^Y RRERZ RRE^E^Z RgVl[ RcV_[ RXGXN\\V RePiY RePaY RbHkHjM RcC`M RXKVT ReKfPdV',
	    'PoRI_I RSS]S RSLSS R]L]T RhVlZ RWWS[ RcV_[ RUDWG RePiY RfPaY R]C[H RcHkHjN RcCaN RXIXRWX ReLfQdV',
	    'PoRH_H RSM^M RTY]Y RTQTZ RXCXM RTQ]Q]X RgWl[ RcV][ RfHfQiY RdQaX RaIkHjN RbC_N ReKdU',
	    'PoTHTX RXFXW RZHZW RhWl[ RbX`[ RfRiY ReRaZ ReNhW RbHkHjN RcC`N RXCWI RTH^H_VRX ReLfQdV',
	    'PoUM\\M RUY]Y RUQU[ RUQ]Q][ RhVl[ RYD^J RcW_[ RWGRL RePiY ReRaZ RYCVI RbHkHjM RcC`M ReLgU ReLdV',
	    'PoUY]Y RUQU[ RUQ]Q]Y R[D^H RWERI RaIkHjN RbC_N RfIfPdU`[ RfQhWl[ RXJVNRR RXJ^P',
	    'PoXH_H RRS^S RRLR[ RVLVZ RXDXL RZLZZ RfRjZl[ ReRbZ RfNhX RcHkHiN RdDaN RRL^L^[\\[ RfLfQdV',
	    'PoSK\\K RSP\\P RSFSZ RWCXF RSF\\F\\P RgVl[ RcV^[ RZT\\Z RePiY RePaY RaHkHiN RbC_N R[WRY ReMfV ReKcV',
	    'PoTK]K RTP]P RRT_T RTCTK R]H^K RgVl[ RZV]Y RXU[W RWWS[ RcV][ RfHfQiY RdQaX RULSR RaIkHjN RbC_N R\\ESG ReMgV RXPWW ReKdT',
	    'PoSG_G RTQ^Q RTKTQ RYCY[ RTK^K^Q RhWl[ RXR\\V RcV^[ RWTRY RfHfQiY RdQaX RYPVV ReNhW RbIkHjO ReNcW RbC`N ReLdT',
	    'PoSM_M R\\Y_Y RVOVY RZOZ[ RTR_Q_X RhWl[ RcW_[ RfHfRjZ RTE^I ReRbY RXJTS R]CZFSJ RfNhX RcDaN RfNcW RbIkHjN RfLgV RfLdU',
	    'PoRJ^J RTN]N RRS_S RgVl[ RZV]X RXNXTZW R[F^J RWVS[ RcV][ RfHfQiY RdQaX RWCTI RUKSQ RXRUY RaHkHjN RbC_N RXOWW ReKdT',
	    'PoRG_G RRO`O RTWZW RY[][ RTRTX RTRZRZW R]O][ RhWl[ RZI^L RcW_[ RXHTL RfHfRjY ReRbY RfNhX RbIkHjN RYCWJ RcC`M ReMdV RfLgV',
	    'PoRG_G RUL]L RUP]P RRT_T RUDUT R]D]T R[V^Y RVWR[ RbD_N ReIePdV`[ ReRgWl[ RaIkIjM',
	    'PohWl[ RXJ]L RXT]Y RWWS[ R^Q[T RcW_[ RXJTN R]F\\I RfHfRiY ReRbY RUFSI RUQSU ReNhX RbIkHjN RcC`N RYCWK RYOWX RfLfQdU',
	    'PoTGYL R\\SaX RSSXX R\\G`L R^UZZ RWISM R_IZN RWVRZ RfRjY ReRbZ RSEYEVK RRPXPUX R[EaE^K R[P`P]W RdHkHjN ReCbN RfMhV RfLdV',
	    'PoUJ\\J RSO^O RXJXY RYC^I RXDRJ R\\Q\\V RURUV RaHkHjN RbC_N R^XRY RfIfQdUcW^[ RfQhWl[',
	    'PoSF_F RSJ_J RTN^N RRR_R RV[Y[ RYCYJ RYRY[ R]T^X RUTRY RbIkHjN RcD`N RfIfPdUcX`[ RfRiWl[',
	    'PoYL]L RZT^T RTY^Y RTPT[ RZO^O^Z RhWl[ RcW_[ ReRaZ RVDRL RXNSP ReHePhX RbHkHjN RVF^F]L RcD`N RTTXT ReLgV ReLdV',
	    'PoRF_F RSO^O RRR_R RV[Y[ RYCYM RYRYZ RgVl[ R]T^W RcV_[ RYFSL RePiY RXG]I RePaY RUTSY RbIkHjN RcC`M ReLfQdV',
	    'PoSF`F RVJ]J RVM]M RRQaQ RTY_Y RTQTY RVCVQ R^C^Q R]S^W RfRjZ ReRbZ RXSVW RfOhX RcHlHjN RdCaN RfLgQeV',
	    'PoSJ^J RRM_M RSDSJ RSQS[ RWQWZ RXCXJ RZQZZ R^D^J RhVlZ RcW_[ RePjY ReRbY RbIlHjN RcCaM RfMgV RXMWT ReLdV RSQ^Q^Z\\Z',
	    'PoRI_I RSX^X RSNSZ RYDYX R[N_N^Y RhWl[ RcV_[ RfHfRjY ReRbY RWMSN RfNhX RbIkIjN RcC`N RSSVSRS R[S^S[S ReMdV RfLgV R^CRE',
	    'PoUW[W RY[^[ RRNR[ RUQUW RTIXIXN RUQ[Q[W RTD\\D\\N RTDSN^N^Z RgVl[ RdOiY RdR`Z RbHkHhN RdMgV RcC`M RdLcW',
	    'PoRE^E RSO^O RST^T RSISO RWEWO RXQXY RZEZO RSI^I^O RhVl[ RcW_[ RePiY RePaZ RbHkHjM RcC`M ReLfPdV R^XRZ',
	    'PoSF^F RRN`N RTU]U RTZ]Z RTQT[ RUHVL RYCYG RTQ]Q][ RhWl[ RcW_[ RfHfRiY ReRaY R\\H[L ReNhX RaIkIjN RbD_N ReNcX ReLgV ReLdV',
	    'PoTH^H RTL^L RTX\\X RZ[][ RTDTL RTQTY RTD^D^L RhWl[ RYR\\V RcW_[ RfHfRjY ReRbY RVLTR RYQWV RfNhX RbIkIjN RcD`N RfLgV RUP_P_U^[ RfLdU',
	    'PoVJ]J RSQXQ RSUXU R\\[_[ RSMS[ R[N[W R_M_[ RhWl[ RYE]F R]G`J RYDSJ RePjY ReRbZ RcHlHjN RdCaM RSMXMX[V[ RfMgW RfLdV',
	    'PoRHaH RRObO RSS_S RXDX[ R\\E\\[ RSK`K`S R[T`X RdW`[ RUVRY RgSkZ RfRcY RWRTX RcHkHjN RdCaM RfHfPdX R_DYERE RfLhV RfLeV',
	    'PoRG_G RTM]M RVX[X RSPS[ RTITM RVSVY RXCYG RVS[S[X RTI]I]M RhWl[ RcW_[ RfHfRjY ReRbY RbIkHjN RcC`N RSP^P^Z\\Z RfLgV ReLdU',
	    'PoZH_H RZQ_Q R[E[Q RYT]T]Y RVK`K`M RdHkHjM RgRjY RfRbZ R_X\\Y RfNiX RYTXZ ReCbN R\\SYS RTETTR[ RVKVTU[ RfLgQeW R_DTE R^NXN',
	    'PoTLZL RRP`P RTXZX RZ[][ RTSTY RTHTM RTHZHZL RTSZSZX R^P^[ RbHkHjN RcD`N RRE_E]NZN RfIfOeScX`[ RfQgUl[',
	    'PoTK^K RTN_N RWUWX RTF^F^K RhWl[ R\\T]U RcW_[ RYTZW RfHfRjY ReRbY RXCYF RSURZ RfNhX RbIkHjN RcC`M RfNcW R^[Z[ RfLgV RTFTR_R^Z RfLdU',
	    'PoRF_F RVI\\I RSO^O RSR^R RRU_U RSKSO RVCVI R\\C\\I RSK^K^O RXW]Y RbHkHjN RcC`N RYIYSWXS[ RfIfPdTbX`[ RfQhVmZ',
	    'PoRF_F RUI\\I RSP^P RSS^S RSV^V RUCUI R]C]I RSL^L^P RhVl[ RcW_[ RePiY ReRbZ RbIlHkN RSLSP RcC`N ReLfQdV RYIZYSZ R^YWY',
	    'PoRE_E RVM]M RRX_X RRERZ RVHVM RVH]H]M RZO^O^U RgVl[ RcV^[ RfHfQiY ReQaY ReNhW RbIkHjN RcC`N RUOXOXUTU RZOZU^U RTOUUXU ReLfU ReLdU',
	    'PoYE_E RVK]K RVVaV RX[[[ RVQ\\Q[[ RdIkHjM RXSXU RfPjZ RfRbZ RTH`H_K R_R^U ReCbN RfMhW RTHTTS[ RfLeW RZCZN_OXQ',
	    'PoRF_F RUO]O RVW[W R\\[^[ RSKS[ RVRVX RYDYG RYLYR RVR\\R\\W RSK_K_[ RhWl[ RcW`[ RUGVJ RfHfRjZ ReRbY R\\G\\J RcHkHkN RdCaN RfOcX RfLgV RfLdV',
	    'PoUG^G RRJ_J RTO_O RUR^R RUV^V RUY_Y RUMU[ R[M[Y RVCSI RXFTP RcC`N RbHkHjN RgIfPeUa[ RfQhWlZ',
	    'PoYE_E RYN_N RVUYV R\\V_V RVRVV RYCYH RZIZN RYPYY R\\P\\Y R_R_V RTH`H`K RdW`[ RgSjZ RfRcY RcHlHjN RdCbN RgHgOhV RTHTTRZ RfLeV R]KVL RaXUZ',
	    'PoSE_E RRI`I RSP^P RSS^S RSW^W RSMSW RVGVK RYCYG R\\G\\K RSM^M^W RhWl[ R[Y^Z RcW_[ RePiY ReRaZ RWYRZ RcHkHiN RdCaM ReMgV ReLdV',
	    'PoRF_F RSI^I RTO^O RRS`S RTZ^Z RTVT[ RTLTP RYCYI RTV^V^[ RhWl[ RcW_[ RVQWS RfHfRjY ReRbY R[Q[S RTL^L^P RfNhX RbIkHjN RcC`M ReMdV RfLgV',
	    'PoRH`H RTK^K RTQWQ R\\R_R RTT_T R\\W_W RWNVR RTNTWWWW[ RYCY[ R\\W\\[ RTE^E_K R_M_[ RgRkZ RgQcZ R\\N\\R RgOiX RdHkHjM ReCbN RgLhV RSSR[ RgMeV',
	    'PohWl[ R]UaX RVUYX RcW`[ RYJUN R^E]I RfHfRjZ RYJ]K ReRbZ RVETI RTQSU RXQXU R[QZU RaQ_T RcHkHjN RdDaN RfOcX RZCXK RfLgV RVOUY R^O]X RfLdV',
	    'PoVH]H RUN^N RT[W[ R[[_[ RRQWQX[ RYQ_Q_[ R[D_H R^QZU R_TZY RXCSI RWQSU RWTSY RfHfRjZ ReSbZ RfOhX RYD[D RbIkHjN RUKUO RUK^K^O RcC`N RfNdW RfLgV',
	    'PoRF^F RSJ]J RRM^M RRR_R RSZ^Z RSUS[ RYFYR RSU^U^[ RhWl[ RcW_[ R\\C[F R]N\\Q RUOTR RUDUF RfHfRjY ReRbY RfNhX RbIkHjN RcD`N RfNcW RfLgV RfLdU',
	    'PoVI]I RTRXR R[R_R RTMTR RTMXMXR R[M[R R[M_M_R RhWl[ R\\V_Y RVVXY RZC_I RcW_[ RYCRJ RfRjY ReRbY RVTS[ R\\TZZ RbHkHjN RcD`N RfHfPcW RfMhW RfLdU',
	    'PoSI`I RTT\\T R[[^[ RSDSI RTPTT RXNXX R\\D\\I RTP\\P\\T RSD`D`I RUM`L_X_[ RdW`[ RWDWIULRO RfKfQk[ RfQbY RWGUL RcIlHjN RdCbN RfNgV RfMeV R[V\\XSX',
	    'PoSI_I RSL_L RRP`P RRT`T RSW_W RSDSI RWDWI RYJYO RYTY[ R[D[I RSD_D_I RhWl[ RcV_[ RVQVT RfHfRjY ReRbY R\\Q\\T RbHkHjN RcC`N ReMdV RfLgV',
	    'PoTH_H RTL_L RWWX[ RTE_E_L RhWl[ RZT_S`U R^V_Y R_OZT RcW_[ R\\MZP R[W[Z RXORUXTXU RePiY ReRbZ RTETLSP RTWR[ RcHkHjN RdCaM R^PZP RWPRP RfLfQdV',
	    'PoTJWJ RTOVO RRTbT RTETT RXNXT RXCXK[K[T R]E`EaT R]W`Y RVWR[ RdW`[ RWDSE RgSkZ RfRcY RcHkHjN RgHgPdX R`J]J R]O`O]O R\\GXG RdCbN RfLhV RfLeV',
	    'PoRP`P R[U_U RSX_X RSZ_Z RSRS[ R[P[U RSR_R_[ RhWl[ RcW_[ RSJXJTN RfHfRjY RSDXDTGWG RZJ_J[M_N RTKXM ReRbY RZD_D[G^GYH RfNhX RbIkHjN RcC`N ReMdV RfLgV RWPWU',
	    'PoRE`E RSMXM RZM_M RUQ_Q RUS^S RUV^V RUY_Y RUPU[ RVCVG R[O[Y RZIZM R\\C\\G RZI_I_M RhWl[ RcW_[ RfHfRjY ReRbY RVNSS RbHkHjN RSITM RSIXIXM RcD`N RfMhW RfLdU',
	    'Po`NkN RR[l[ RWJW[ R`D`[',
	    'PoSFkF R`PjP RR[l[ RWMW[ R`F`[',
	    'PoZL_L Rb[k[ RUIU[ RZCZZ RcCcZ RlUkZ RkJbN R_YR[',
	    'Po_GiG RRLlL R[U_U RWFWL R_C_T RXNTS RhOgRcW^YU[',
	    'PoUE_E RRJlJ R[QaQ RUOUZ R[L[Y RgDjH RaXRZ RdCdLfZk\\lW',
	    'Po]HlH RXL\\L RTITY RXCXW RdCdN R`ObTfYl[ R^NjNhScX\\[ R]VRX',
	    'Po_GiG RSMkM R_SiS R_[l[ RWFWM R`C`Z RXPUY RWRYV\\Ye[',
	    'Po_GiG RRLlL RZU_U RWFWL R_C_U RfOkT RXOST RfScW_YU[',
	    'PoSEkE RSQkQ R_ViV RR[l[ RWTW[ R`G`O R_Q_[ RbIkM RaE^ISN',
	    'Po]ElE RXM\\M R_SjS R][l[ RTITY RXDXX R_E_[ R_LjLjS R]WRZ',
	    'Po_FhF RRJlJ RRSlS Rd[g[ RXEXJ R_T_Z R_C_J RWMgMg[ RVWS[ R^K]M R\\O`P RWMWSVX',
	    'Po^GkG R]LlL R]QlQ Rd[h[ RTITY RXCXW ReCeL RhLh[ R`SbV RXL[LXL R\\VRY',
	    'Po_GhG RSLkL RXS]S ReSkS R^[l[ RTQT[ RXFXL RXNXZ R_C_L RaQa[ ReNe[ R^YR[',
	    'Po_FiF RRJlJ RWQhQ RTZjZ RTLT[ RWEWJ R_L_X R_C_J RjLj[ RYM[O ReMcO R_QcUhW R_Q[UVW',
	    'PoSE^E RYO]O RfOjO RXT]T ReTkT R^[l[ RTST[ RXPXZ RaSa[ RePe[ R`HdJ RdK`O RWKSO RTHXJ RYEWL ReEcL R^F]N R`EkEkJjN R^YR[',
	    'Po]IlI RXL[L R^PkP R^SkS R^WkW R\\[l[ RTITY RXDXX R^L^S R^EeEe[ R^LkLkS R\\WRZ RjDbE',
	    'Po_FiF RSJlJ RUNlN RWQ`Q RZW\\W RXDXJ R\\Q\\W R_C_J ReWh[ RXSWW RhSaZ RcRfY RfKiL RhPfV R`U\\YW[ RlWk[i[ RbKdW RUNUTSZ',
	    'Po_FhF RSIkI RUMkM RWQaQ RY[\\[ RXEXI R\\Q\\Z R_C_I R_SaW ReKiL RXSVX RUMUTSZ RbJeVk\\lX RjPgUa[',
	    'PoUElE RWJ`J RbJlJ RaUiU RV[l[ RZTZZ R\\G\\R RaRaZ RfGfR RUEURSZ R\\JZMWP R\\J^N RfJdMaP RfJhMlP',
	    'Po]EkE R^HjH R]KlK R_QjQ R_TjT R_WjW RTITX RXCXW R_M_W RdCdK R_MjMjW RgXl[ RbY\\[ R[LXL R\\VRX',
	    'PoaFjF RYF^F RVPiP RVRiR RZXgX RXYcY Rf[k[ RTETK RTST[ RVMVR R_M_X RVMiMiR RSUkUk[ RbCbKlJkK RdVhY RYCZKRK R^JUK',
	    'PoUElE RWK_K RaKlK RaUiU RV[l[ RZTZZ R\\H\\Q RaRaZ RgHgQ R[K_O RfKlQ RfK`Q R\\KWP R^GWH RiGbH RUEURSZ',
	    'Po]HlH RTJ[J R`KiK RTP[P RXT\\T RTFTP RTSTZ RXPXY RTF[F[J RTL[L[P R^N^P R_R_Y ReNe[ R`DiDiK R^NkNkQ RWCVF R_RjRjYgY R\\YRZ',
	    'PoSElE R^RbT R_FWN`R RXNRP RZKiKfPaVYYT[',
	    'Po`GlG R`C`L RXOcT R\\JVPRR RZLiLgPbV[YT[',
	    'PoRFlF Rb[k[ RbFbZ RlWk[ RTP[S RjLbP RYFWMRQ RXL^L\\TWZT[',
	    'PoRD]D R^NlN RfEf[ RVPYS RXVS[ RZOVY RWDSO RUK[KXV RjD_F',
	    'PoSD^D R_MlM RWPZS RgMg[ RYVT[ RTOYQ RVIRQ R[OWY R_F_T[[ RWDTO RVJ\\JYV RjD_F R_M^X',
	    'PoSD]D Re[i[ RXVS[ RTOYQ RfPa[ R[OWX RcL^W RWDTO RUJ\\JYV RaC]O RcI`T RhJdX R_IlIlQj[',
	    'PoRD^D RfMjM RYVT[ RTOYQ RaPbTgYl[ R[OWX R_PjPhTdX][ RWDTO RUJ\\JYV RbC`L RbGkFjM',
	    'PoRD^D R]NlN RgWl[ RYVT[ RaV\\[ ReQiY RTOYQ R[OWY RcQ`X ReEdMgV RWDSO RUJ\\JYV RjD^F RdIcT',
	    'PoRD^D RhLlL R`DhDhL RlJkL RYVT[ RTOYQ RaQbTgYl[ R[OWY R_PjPhTdX][ RWESO RUJ\\JYV R`D_L',
	    'PoSE]E R`LjL R`SjS R\\[l[ R`E`Z R`EjEjZ RYUT[ RTOYR RVHRQ R\\OWX RWETO RVK\\KYV',
	    'PoSD]D R]QlQ R_H_Q R_HjHkQ RgVk[ RXVS[ RdCdQhY RSOXQ R[OVY RcU_Y\\[ RWDTO RUK[KXV RdLcU',
	    'PoRD]D ReElK RYVT[ RcF\\M RTOYQ R[OWY RcCbG ReL_O RgOeR^T RjSfW^[ RVESO RUJ\\JYV',
	    'PoRD]D R_JkJ R_RkR R]ZlZ ReCeZ RXVS[ RSOXQ R[OVY RWDTO RUK[KXV R`E]N',
	    'PoRD^D R_ZjZ R_Q_[ R_QjQj[ RiIlO RSOZR RWDTO RcD_NjL RUK]KZSS[',
	    'PoSD]D R`FlF R_JiJ Re[i[ RVPZR RXVS[ RcP]V RgTaZ RaC_H RiOfV R[OVX RWDTO RUK\\KXV RhK_OkPj[',
	    'PoRD\\D R[ZlZ R^H^Z RbGbZ RfHfZ R^HjHjZ RVPYS RWVS[ RZPVY RWDSP RUK[KXV RcCbK',
	    'PoSD]D R_QgQ R_VgV Re[j[ R_L_W R_LgLgU RTOYQ RaD]M R`HlHlUk[ RWEUKRQ RVK\\KZRXWT[',
	    'PoRD]D R_IjI R]OkO RdCd[ RSOXQ RWETO R`E^L RUK[KXVS[ RdObR\\X RdOgTmX',
	    'PoRD]D RSOXQ RfCjE RWDTO R_HjH R^MiL R^RlP RjSeW][ RbCcMeYk[lX RUK\\KXVS[',
	    'PoRD\\D R^ElE R^SkS R][l[ ReSeZ RVPYS RhKjO RWWS[ R_KaQ RcKfQ RjGhL RZPVY R`G^L ReGcL RVDSP RUK[KXW',
	    'PoRD]D R]SlS R`[d[ RdQd[ RdFeJ RXVS[ R_MiLdQ RiElJ RSOXQ R`E]K R[OVY RWDTO RUK[KXV RiC_E',
	    'PoRD\\D R]GlG ReUj[ R`MbQ RWVR[ RdCdRgX RiLkR RUPYR RZOVY RbU^Y[[ RVDSP RUK[KXV R_J]R RiJfR RdLcU',
	    'PoRD\\D R]HlH RUJ[J R_[d[ RdCd[ RVPYS RgTlY RgDiF RWVS[ RdP\\X RjNfQ R^L`P ReOhW RZPVY RdJgT RWESP R[KXV',
	    'PoRD]D R]GkG R^QkQ R^K^Q RdCd[ R^KkKkQ RUPXQ RfSlY ReQgU RXVS[ RdObS\\Y RZOVY RWESO RUK[KXV',
	    'PoRD]D R]GlG R^QjQ Rf[k[ R^K^Q RdCdK RgQgZ R^KjKjQ RkXk[ RXVS[ RaW][ RTOXQ R[OVY RWETO RUK\\JXV RbR`Y',
	    'PoRE\\E R_JiJ R^M^V R_D_J R_DiDiJ R^MjMjV RUPYR RXVS[ RfXlZ RZOVY RdVaY\\[ RWESP RUJ[JXV RdQcW',
	    'PoRD\\D R`LhL R\\ZlZ ReElM RWVR[ RcG]M RUPYR RjOfZ RZOVY RdCbH R^P`V RVDSP RUK[KXV RdOdU',
	    'PoRD\\D R]KlK R^OjO Ra[d[ RdCd[ R^GjGjO R^QaT RUPYR RWVR[ RjQgU RdSiXlY RZOVY RdU]X RVDSP RUK[KXV',
	    'PoSD]D R^FlF R]NlN R_ZjZ R_R_[ RdCdG R_RjRj[ RSOXQ RXVS[ RiHhM R[OVY R`HaM RWETO RUK[KXV',
	    'PoRD\\D R]FlF RaNjN RaRjR RaVjV R]YlY R]L][ RaJaV RaJjJjV RSOXQ RVDSP ReCdJ RUK[KXUT[',
	    'PoRD\\D R\\GlG R]QjQ R]VjV Rd[k[ R^L^W RdKd[ R]MkMjU RkXk[ RVPYS RSOXQ RfHlN RWVS[ RaI\\N RZOVY RcC_K RVESO RUK[KXV',
	    'PoRD]D R`GiG R^HfH RaSkS R\\TfT RXVS[ RlKkN RbEdKfMjN RSOXQ RaQcWeYi[ RZOVY RfDjE RWDTO RUJ[KXV RhJcM^N RhVbY\\[ RlXk[i[ RePjP',
	    'PoRD\\D RaDgD R^IlI R^VkV R^ZkZ R^E^O R^Q^[ R^QkQk[ RlMlP RXVR[ RhNkP RUPYR RZOVX R^EeDgN RVDSP RUK[KXV RdN]N RjCcD',
	    'PoRD\\D R]MkM R]RkR R[VlV R]I]R RdId[ R]IkIkR R_DaG RWVR[ RUPYR RZOVY RhCfI RVDSP RUK[KXV',
	    'PoSD[D R\\DkD RUKZK R\\T`T R[ZlZ R\\L\\U R\\L`L`S RhSlW RhSeW RfNjV RjNgV RTOXRUZ R_XcX_X RfLhT ReJkJhT RVERQ RZLVX R`D`JdIcTbX',
	    'PoRD\\D R\\HlH RcLhL R^OkO R\\SlS R^D^O RcCcL RdOd[ RhDhL RdSjY RWVR[ R_X[Z RcS]Y RUPYR RZOVY RVDSP RUK[KXV',
	    'PoRD[D R]FlF R^JkJ R]MlM R]TjT RdPd[ RgVjZ RbTgV RfMkR RWVR[ RaM\\S RUPYR RZOUY RcH`P RcT_X[Z RVDSP RUK[KWV RcDaN RfP_Q',
	    'PoRD]D R_HjH R^OkO R^RkR R^VkV R^K^V R_D_H R_DjDjH R^KkKkV RiYl[ RYVS[ RSOZR R[OWX RbX\\[ RVDSO RUJ\\KYV',
	    'PoRD\\D R^NiN R[[l[ R]Q]Z R^D^N RbQbZ RfQfZ R^DiDiN R]QkQkZ RdIgL RXVS[ RSOXQ RZOVY RdFbK RVDSP RUK[KXV',
	    'PoRD]D R^IjI R^MjM R\\UlU R^FjFjP RSOXQ RXVS[ RcCcF R^F^PhQjS ReVhYl[ RZOVY RcV`Y\\[ RWDTO RUK[KXV RdQbX',
	    'PoRD[D R`OkO R\\UlU R]G]J RaKaU RfOfU R]GkGlJ RiXk[ RdCeF RWVR[ RUPYQUY RaX\\Z RVDSP RUKZKWV RhJ`K',
	    'PoRD[D R[GlG RUKZK RiKlK RgYiY R\\N\\Q R^R^Y RcOc[ ReCeK RiCiK R^RjRjY R\\NlNlQ RWWS[ RSOWQ RYPUY RVDRQ RZLWW RaCaKeK R^E]K',
	    'PoRD[D R\\FlF R`IhI R]PkP R]SkS R]WkW R\\[l[ R]L]P R`C`I RdIdZ RhChI R]LkLkP RSOXQ RWVS[ RZOVY RVESP RUK[KXV',
	    'PoRD\\D R_GlG R_KjK R_NjN R\\QlQ Rf[i[ R_I_N R_IjIjN RWVS[ R_S[W RbU\\[ RfTdZ R_C]I RSOYQVY RVDSP RUJ[KXV R^UkTi[',
	    'PoRD\\D R_JjJ R^RkR R^VkV R]G]J R^N^V RdJdV R^NkNkV R]FlGlJ RcCeF RWVR[ RgXkZ RUPYR RZOVX RaX\\[ RVDSP RUK[KXV',
	    'PoSD]D R_IjI R_LjL R_OlO Re[j[ RaUbY R_FjFjL RgUiW RXVS[ RdCdF RdUeX RVPZR R[OVY R^V\\Z RWDTO RUK\\KXV R_F_RkSj[',
	    'PoRD[D R[GdG RdJlJ R\\QbQ Rh[l[ R\\[_[ R\\K\\Q R_Q_[ R\\KbKbQ RgCgPh[ RlXl[ RUPXR RiElG RaTbW R_D`G RfRcZ RYPTZ R\\TZY RWDSP RUKZKVW RgLeV',
	    'PoRD\\D R^HkH R\\KlK R^QjQ R^TjT R^WjW R^E^I R^N^W RdCdK R^NjNjW R^EkEkI RWVS[ RgXl[ RSOXQ RZOVY RaX[[ RVESP RUK[KXV',
	    'PoRD\\D R[TkT RcOc[ R^P`S RUK\\KaL RfHkM RWVR[ RiPgS RbJ]O RcSgXlZ RSOXQ RZOVX R`C_HcH`M RfEkEiIfLcM RcS_XZZ RVDSP R^H[KXV RdE`EdE',
	    'PoRD[D R\\FlF R]IkI R_OiO R_ViV R[[l[ R]L]O R_Q_U RdCdI R_QjQjU R]LkLkO RXVS[ RaWa[ RSOXR RgWg[ RZOVX RVDSP RUK[KXV',
	    'PoSD\\D R^IcI RfIkI R^OkO R^SkS R\\WlW R^D^I R^L^S R^DcDcI RdLd[ RfDfI R^LkLkS RfDkDkI RXVS[ RSOXQ RZOVY RWDSO RUK[KXV',
	    'PoRD[D R]IkI R^LjL R\\OlO R\\TlT R]WkW R]D]I RaEbI RdIdO RdTd[ RfEfI R]DkDkI RXVS[ RgQgS R`Q`T RSOXR RZOVY RVESP RUK[KXV',
	    'PoRD[D R]DkD R^JjJ R^MjM R\\OlO R^UjU R^XjX R[ZlZ R^R^X R^G^M RdGdM RdRdX R^GjGjM R^RjRjX RXVS[ RSOXQ RZOVX RVDSP RUK[KXV',
	    'PoRD\\D R`JhJ R]RbR ReRkR R]M]R R]MbMbR ReMeR ReMkMkR RgVl[ R_VbY RdCkJ RWWS[ R_W[[ RdD\\K RSOXQ RhTeZ RZPUY R_T]Z RVDSP RUK[KWW',
	    'PoRD[D R_HiH R_QjQ R_TjT R_WjW R_N_W RdCdE R^NjNjW R]E]FlElH RWVR[ RgYl[ RSPXR RZOVY RaJ]K RaY[[ RVDSP RUK[KXV RiK^N RdHeL\\N',
	    'PoRD[D R]ElE R^HkH RfLkL R]NkN R^SjS R^VjV R]J]N RdCdH ReJfL R^QjQjX R]JkJkN RcJ`M RWVS[ RSOXQ RZOVY R^Q^XhYl[ RVDSP RUK[KXV RaZ[[',
	    'PoRD[D R\\FeF RUJZJ R[JlJ R[N_N R\\Q_Q R[U_U RbUeU R_J_X RiEkG RhUb[ RlWj[k[ RkNfX RSOXQTZ RVESQ RZLVX RbNeNbN ReQbQ RhChQiZ R`CaJcX_XZY',
	    'PoRD[D RUKZK R[KlK RbUeU R_XeX R_K_X RlWk[ RSOWQ RcFeH RjDkH RhUb[ RjNfX R^C\\I RYQTZ RcDaI RVERQ RZLVX R_N[N ReNbN R_Q\\Q ReQbQ R_U[U RhChQiZ RbKbX[Y',
	    'PoeLlL RUOgO RXDeDeL RlIlL RXJTN RXQ]WbYi[ RfQaV]YT[ RaYl[ RXEWK',
	    'PoRE^E RhLlL RRW^W RRERZ R`DhDhL RlIlL RTJ]S R`PbTeXl[ R[IVT R^OjOhScX\\[ R`E_L',
	    'PoTK\\K RhMlM RTP\\P RTFT[ RaDaK RaDhDhL RlJlM RaPbTfXl[ R_PjPhTdX][ R]DTE R]URV',
	    'PoRI\\I RhMlM RSP\\P RWIWY RaDaK RaDhDhL RWDXG R`ObTfXl[ R^OiOgTcX\\[ R]WRY',
	    'PoTK]K RhMlM RTO]O RWZ[Z RTH]H]O RaDaK RaDhDiL RlJlM RaPcUgYl[ R_PjPhTdX][ R\\DTETSR[ RTS\\S[Z',
	    'PoSF`F RTK_K RiMlM RTT^T RYCYK RYNYT RTN_N_U RbDbK RbDiDiL RlJlL RbPdVhYl[ R`PjPhTdX^[ RTNTTS[',
	    'PoRE_E RWJ^J RiMlM RUO_O RTY_Y RTETX RbEbK RbEiEiL RlJlL RZJZQ^U RcQeVhYl[ RZPWV RaPjPiTeX^[ RWGVM RZLYT',
	    'PohMlM RRP]P RXLX[ RaDaK RaDhDiL RlJlM RXQ\\V R[ESL RSE[I RaPbUfXl[ R_PjPhTdX][ RXPUURX',
	    'PoRF^F RSJ]J RhMlM RRMRP RUQUX RXCXJ RRM^M^P RaDaK RaDiDiL RlJlL RbPcUgYl[ R`PjPiTdX^[ RUQ[Q[Y^W RUXSZ',
	    'PoRF^F RSI]I RiMlM RUO[O RRLRO RURUX RXCXI RUR[R[X RRL^L^O RbEiEiL RlJlM RbQcUhYl[ R`PjPhTdX][ R^WYX RbFaM',
	    'PoiLlL RSL_L RVS]S RVW]W RVOV[ RcEiEiL RlIlL RUE_I RbPdUhYl[ R`OjOhUdY_[ R]CZFSI RUQ]P][[[ RXJUPVS RbFbM',
	    'PoTG[G RRL_L RiMlM RUU]U RUY]Y RVOV[ RWCWL RTQ]Q]Y RbEiEiL RlJlL RZN\\O R\\ISQ RbQdUhYl[ R^E[J R`PjPhTeX^[ RbFaM',
	    'PoTJ_J RiMlM RVN_N RUT_T RXKXT R]K]T RTE_E_I RcEcK RcEiEiL RlJlL R]V^Y RcQeViZl[ RWVU[ RaPjPhUdY_[ RTETRSZ',
	    'PoZJ^J RiMlM RSN^N RSS^S RSESN RXOXX RZE^E^N RbDiDiL RlJlM RbPdUhYl[ R`PjPhTdX^[ RXDSE RbFaM R_WRY RSJWJ',
	    'PoRJVJ RhLlL RRN\\N RRR]R RRERN RXRXY RXF\\F\\N RaDhDhL RaPbUfYl[ RlIlL R^OjOhTdX\\[ RWDRE RYJ]J R^XRY RaF`L',
	    'PoRF^F RSI]I RiLlL RRQ^Q RTT]T RWX]X RRXZX RXCXI RYRY[ RRLRM^L^N RcEcK RcEiEiL RbOdUiYl[ RVNTT RaPjPhUdY_[',
	    'PoRG_G RiLlL RTM]M RVX[X RSPS[ RTITM RVSVY RXCXG RVS[S[X RTI]I]M RbEbK RbEiEiL RlJlL RbQdUhYl[ R`PjPhUdY_[ RSP^P^Z\\Z',
	    'PoRF_F RTK^K RTN^N RRQ`Q RTY^Y RTITN RTTT[ RYCYT RTT^T^[ RTI^I^N RcEcK RlIlL RcQeUhYl[ RaPkPiUdY_[ RcEiEjLiL',
	    'PoSF^F RRL_L RhMlM RXCXF RbEiEiL RUGVK RbQdUhYl[ R\\G[K RXLVNRP R]OZRWVRX RlJlM R`PjPhTdX^[ RbFaM RXL[R[ZV[ RYOWRST R[Q^V',
	    'PoRE^E RiMlM RVM]M RZU^U RRX_X RRERZ RTOTU RVHVM RZOZU RVH]H]M RZO^O^U RbEbK RbEiEiL RlJlM RbQdUhYl[ R`PjPiTdX^[ RUOXOXUTU',
	    'PoTIWI RZI^I RiMlM RTM^M RRT^T RTETN RXOX[ RYE^E^N RbEiEiL RlJlM RTPUS R\\P[S RXTSZ RbQdUhYl[ RXU]W R`PjPhTdY_[ RXDTE RbFaM',
	    'Po`JdJ RePkP R`PdP R[U^U RSFSU RUKUO RZFZ[ RZF^F^U R`E`[ RfEjEkKlL RhWl[ RiVd[ RfRjY RkQgY RXDSF RdD`E R\\K\\O RWHWTRT RdU^V RWOVX RfFeM',
	    'PoSD_D RVH]H RUJ^J RSO_O RTQjQ RR[l[ RSDSO R_Q_[ RbDbH ReUiY RXU\\Y RXUTY RZJWM RaJdMjO RYK^M ReScX RYSVX RaJjJgMaO RZGYK RbDhDhHkGkH',
	    'PoSD_D RTG^G RhGlG RaIjI RTL^L RVNaN RRPlP RZTeT RZZdZ RUUU[ RYCYG RYIYL RZWZZ R^I^L RbDaG RZWdWdY RbDhDiG R\\OTS RbJkL R[I^I RZPYSeQdRlS RTISN RWITI R\\IYI RhJ`M RUUjUi[f[ RVIYI',
	    'PoRNlN RUWkW R`[f[ RWETX R`E`K_O^W RWEhEhOhRf[',
	    'PoROlO RUYgY R_N_X R_L_U R_E_R RWEhEhZ RWEWLVQVVU[',
	    'PoRNlN RUWkW R`[f[ R\\HcJ R[QbS RXETY RXEhEhLhQf[',
	    'PoWGjG RRQlQ RUXkX R`[e[ RYCWGSK RWJUY RWJhJhQf[ R`J^X',
	    'PoWGkG RRQlQ RUXkX Rb[f[ RWGRL R\\LbO R[SaV RXCVI RWKUY RWKhKhTg[',
	    'PoRFkF RTJjJ RRRlR RUXkX Ra[e[ R_C_J R]VZYV[ RWMUW R_M]W RWMhMhRf[',
	    'PoRN^N RSW^W Rb[k[ RW[[[ RcFc[ RgCgU RWHYK RVQYT RlWk[ RkIcL`M RkIkShS RUETKTOSU RTE]E]T[[',
	    'PoTFkF RUIiI RSMkM RRTlT RUYkY R`[e[ R_C_M RWPUZ R_P]X RWPhPf[',
	    'PoTG]G R_GlG R_MkM RRP^P RSW^W RePeZ RlXl[ RWRXU RhJlN RXLYN RUCRJ RcGaM ReCeG R[[W[ RjPk[i[ RUJTPSX RUJ\\J\\S[Z RaPaU`Y',
	    'PoVL^L Rb[k[ RVCVZ RbCb[ RjJaN RlVk[ R_XR[',
	    'PoUH^H RaOkO RRTlT RUCUO R_P_[ RaCaN RkKkO RiF`I R^MTO',
	    'PoVH^H RaOkO RZ[e[ RVCVO RZSZZ RaCaN RgSlX RkKkO RfWeZ R[P`R RUSSX RfPaUU[ RiFaI R_MRO',
	    'Po^McM RRNZN RRVZV Re[l[ RRFRY RVFVV RRFZFZV R^D^Z RfDf[ RlXl[ RkKeN RcX[[',
	    'PoUIiI RUNiN RVS_S Ra[k[ RUEUN RVPV[ R_E_N RbPb[ RUEiEiN RlWl[ RjRaU R]YR[',
	    'PoUG^G RaMkM RRPlP Rb[l[ RUCUM RbCbL RbPbZ RWV[X RkJkM RkXl[ RZPVTRU RVT_T[XS[ RjEaH RkSaU R^KTL',
	    'PoWEaE RVJiJ RVL^L RVO]O RaOkO RUViV R`[l[ RVRUV R`V`[ RTGiGiJ RTTiSiW RlXl[ R`KaO RcXhY RZOVRRT RYCVFRH RVKVO RVFVJ RSOXPaQ_S]W[YR[ RhL`M',
	    'Po][k[ R]F][ RlWk[ RgDRH RiKUN RlQRT',
	    'PoaWeW RX[k[ RXEXZ ReCeV RlXk[ RiIkR RaI^R R\\DRG R\\KRL R\\QRS',
	    'PoX[k[ RXEXZ RlXk[ RiD_I RiK_P RhS_W R\\DRF R\\KRM R]QRS',
	    'PoUG]G RaMlM RbSiS R^SeS R[TbT RWT^T RTT[T ReVlV RcWjW R^WeW R[WbW RWX^X RUX[X RRXYX R^[k[ RUCUM R_P_[ RbCbM RlJlM RjFaH R^LTM R^PVQ ReOYP',
	    'Po[J_J R^[j[ R_O_[ R_C_J RiFkI RkXj[ RVHRI RYEUI ReEjG RfH_KXNRN RgMUP R[SUT R^SWS RfRaR\\S RdR^S RjRcR RYWRX R^VWW RaVZW ReV^W RhUbV RkUeV',
	    'PobLkL RYL]L RUP[P RcRiR R^RfR R\\ScS RWS^S RTTZT RbViV R^VeV R]WcW RWW^W RSWYW R_[k[ R_O_[ RbEbL RbEkEkL RlXk[ RWISM RXDVK RRF^F]L R^OWP RfN[O',
	    'PodHlH R_VjV RX[k[ RXEXZ R_N_V RdCdN R_NjNjV RlXk[ R\\DRF R\\KRM R]QRS',
	    'Po]EkE RfPkP R^WkW RX[k[ RXEXZ R^J^W RgEgP R^JkJkW RbEbO_R R[DRF R[KRM R\\RRS',
	    'PocSiS R^SeS R[TcT RWT^T RTTYT RdVkV R`WgW R[WbW RWX^X RRXYX R^[k[ RSE]E]N R_P_[ R`EjEjN RZJSM ReF_H RgJ`M RYFSH R[PUQ ReOYP',
	    'PoSN\\N RdWiW R[[k[ R[P[Z RdEdW RiTiX RlXlZ RZD]G RVERI RWJSN R[J]O RWHUM RVPUY RiD_F RjJ_K RjN_P',
	    'Po_PgP R_UgU RfXjX RX[k[ RXEXZ R_K_U R_KgKgU RlXk[ RaC]L R\\DRG R[KRM R\\QRS R`GkGkSjX',
	    'PoTJ[J RTP[P Rc[k[ RTETV RRE\\E[[ RcFc[ RlWk[ RiD^G RjJ^L RlQ]S R[URW',
	    'Po]HlH R]NdN RX[k[ RXEXZ RhDjF RfSaX RjTkXk[ RiKeU RhWjW R[DRG R[KRL R\\QRS ReCeKgV R`J`O_U',
	    'Po^HjH R[NkN ReWjW RX[l[ RWEWZ RcCcN RfNfW RjTjW R_T[X R_D\\K RaN_U RZDRF R[KRL R[QRS',
	    'Po]MkM RX[k[ RWEWZ RdDdY RcMiS R`R\\V RlXk[ R^E`J RbM`S RcLaQ RjEgJ RZDRF RZKSL R[RRS',
	    'Po_NjN R]SlS RX[k[ RWEWZ RdJdY RlXk[ RcD^I R_K]P RZDRF RZKSL R[RRS RgH^I RhEjHaI',
	    'PoRFlF RVLhL R_[l[ RSOSR RVHVL R_C_F RVHhHhL RSOkOlR RlYl[ RfPXR RUUgU RjXUX R_R_[',
	    'Po]HlH RaXdX RX[k[ RXEXZ ReCeX R_KaN RhDkE RkLhN RlXk[ RbR\\T R\\DRG R\\KRM R]QRS RdLhRkU',
	    'PoRE]E RSP\\P Rd[l[ RSISO RSI\\I\\O ReFe[ RlWk[ RTRUW RZRYX RjD`F RlQ_S R]WRY RkK_L',
	    'PoRI[I R`KcK RS[W[ RWCW[ RdCdK RcQc[lZk[ RhElI R^G[J R[ORQ RhIeLbN\\O RhO\\Q RbT[T RfS_T RhSaT RjSdS RbW[X RdW^W RkVgWbW',
	    'PoX[k[ RXEXZ RjEhH R`P_S RjPhS R`E_H RdIjL RlXk[ R\\DRG R\\KRL R]QRS ReDdJ_M RdNdUaW^X RdTiW',
	    'Po^GkG R]MlM R_WiW RW[k[ RWFWZ R_Q_X RcDeG R_QiQiW R`IaL RhIfM RlVk[ RZERF R[KRM R[RRS',
	    'Po^OiO RfXjX RX[k[ RXEXZ RdIdW RcPfRhT RdO]V RkXk[ R`E\\I R_J`M RhJfM R[DRF R_GkGkRjX R\\KRM R\\RRS',
	    'PoRQXQ Rf[k[ RfFfZ RkWk[ RUHYL R[SaY R[G`L RTSXWR[ R_VZ[ RWJRN R_IZN RREXEUM RXRUY RZEaE]L RZQaQ]X RjDbG RkKbL RlQbS',
	    'PoaGiG R^GgG R[HcH RYHaH RUH^H RcJkJ RaJiJ R^JfJ R[KdK RYKaK RUK^K RSKZK R^NlN RUFYE^E_M RXPX[]Z][ RePe[lZl[ R\\ORQ RiO_Q RfC\\E R[SST RkR`T R]VTW RkV`W',
	    'Po\\HaH RfHjH R]MaM RfMjM R\\SaS RfSkS RX[j[ RWFWZ RaDaY RfDfY RlVk[ RZDRF RZKRL R[RRS',
	    'PoXH_H RUI[I RSL`L Rf[l[ RfFf[ RZL_Q R[D^G RYDTH RlVl[ RZKUPRQ RYRTT R\\R[UUW R_U]WU[ RjEbG RkKaL RlQaR',
	    'Po`HiH R^HgH R]KkK RX[j[ RWEWZ RfEjI RcD^H RfKhNlO RlWk[ RdJ`N\\O ReO]R RgR^V RhUbX]Y RZDRF RZKRL R[RRS',
	    'PoTH[H RTL[L RSS]S RSW]W RSZ]Z Rd[k[ RRDRL RSOS[ RSO]O][ RRD^D^L ReFeZ RkWk[ RjD_G RkK_M RkQ_S',
	    'Po]IiI R]MiM R`WgW RX[k[ RWEWZ R]E]M RaPaW RcEcM RgTgW R]EiEiM RbOeR RkXk[ RhQjU R]Q[V RZDRF R[KRL R[QRS',
	    'PoVJ]J Rf[l[ RV[X[ RSNS[ RSNXNX[ RfFf[ RlWl[ RUPWR RUUWW RTEZD_J RZDSK R^S`[ RZS\\[ R\\MZT R_M^T RjDaG RkKaM RlR`S',
	    'Po`JgJ R^QcQ R^TcT RfYjY RX[l[ RXEXZ R^M^X RfNfU RjMjX RdDhIlJ RdD_I\\J R^McMcXaX R[DRF RZKRM R[QRS',
	    'PoRF`F RVI^I RTM`M RTS_S RTW_W Re[l[ RTPT[ RZIZM RfFf[ RlWl[ RWCTL RjDaG RTP_P_[\\[ RkKaM RlR`S',
	    'PoTH_H RTL_L RTX\\X Rf[l[ RZ[^[ RTSTX RTETL RTE_E_L RfFf[ RlXl[ RYR\\U RVNRR RYRWV RjEaF RUP_P_U^[ RkKbL RkRaS',
	    'PoaIlI RbLjL RbOjO RaSkS RW[k[ RWEWZ ReCeV RbFjFjL RaXkXl[ R]SaW R_P\\W R\\O^U R\\E_E\\L_M]V RZDRF RZKRL RZRRS',
	    'PoSI^I RSL^L RT[X[ Rf[l[ R\\[`[ RSESL RROXOX[ RSE^E^L RZO`O`[ RfFf[ RlXl[ RZQ]T RRQUS R^VZX RXURW R_U[W RjDaG RkKbM RlQbS',
	    'PoSH\\H RZM_M Rf[l[ RfEf[ RlWl[ RYL^Q R[E^H RRMXMSR RWCUH RTISM R[I[N RYQTS R\\SUW R^U\\XU[ RjDaF RkKaM RlR`S',
	    'PoRHaH RTK_K RSQ_Q Rf[l[ RTNTQ RTETK RTE_E_K RSN_N_Q RgFg[ RjDbG RRT_T[XT[ RYCYPVW_Z RlWl[ RkKaL RlQaS',
	    'Po\\FkF R^MiM R^PiP R\\SkS RX[k[ RWEWZ R^J^P RaCaH RgCgH R^JiJiP ReUiY RcSeV RlXk[ RcTaW\\Y RcPbV RZDRF RZKSL R[RRS',
	    'PoSE^E RbFlF RSH_H RUNlN RbTjT R_ThT R]TfT RWT_T RcWkW R_WhW R]WeW RZWbW RVX_X RYCYL RcD`H RWH^K RbFeIlL R`Q`[kZk[ RiFfJ`L RYGVKRL RUNUTTY RfO[Q',
	    'PoSF_F RRJ`J RSQ^Q RST^T RSW_W Re[l[ RSMST RSM^M^T RfFf[ RlWl[ RYCZF RVGVJ R]G\\J RjDaG RkKaL RkQaS RYMYZS[ R_ZZZU[',
	    'Po[S_S RWY\\Y Re[l[ R\\[_[ RSPS[ RWUWZ RZGZN R[P[S RWU\\U\\Y RSP_P_[ RfFf[ RlWl[ RRI_I]L RWF\\I RXPVS RTE^EYG R[IWJRM RjDaF RkKaL RlQ`S',
	    'PobGlG RWN[N R[P^P RbUiU R^UeU R[UbU RWV^V ReWkW R^XeX RYX`X RWY^Y RRYYY R^[k[ RTHTQ RWJWN RYCYG RWJ\\J\\N R_R_[ RTH^H_P R]D_G RUDRG RcGdKhNlP RdCaJ RjGhJeM`O R[XUY RgWbW R[RVS ReQYR RZUTV',
	    'Po\\FkF R[MlM R^TiT R^XiX RX[j[ RWFWZ R^P^X RaFaM ReFeM R^PiPiX R]H^K R_C`E RiHhK RhCeF RlVk[ RZDRF RZKRL R[RRS',
	    'Po^KjK R^MjM R\\PlP R^UjU R^XjX RX[k[ RWEWZ R_G_M R^R^X ReHeM R^RjRjX R]IjHjM RlXk[ RaD\\I R`EfEdH RZDRF RZKSL R[RRS',
	    'PoRF`F RWM[M RTP_P RUU^U RUX^X RW[a[ RU[_[ RR[\\[ Re[l[ RTITP RURUX RWKWN RYCYF RWK[K[N RUR^R^X RTI_I_P RfFf[ RlXl[ RjDaG RkK`L RlQ`S',
	    'PoVH]H RSPXP R[P_P RSS_S Rf[l[ RSKSP RVDVH RSKXKXP RYQY[ R[K[P RVD]D]H R[K_K_P RfFf[ RlXl[ R[U_Y RYS]W RYSSZ RjDaF RkKaM RlR`S',
	    'Po\\ElE RaLfL R]NjN R^SiS R^ViV R\\XkX RX[l[ RXEXZ R]H]N R^P^V RaJaL RdCdE RaJfJfL R^PiPiV R]HjHjN R[DRF R[KRL R\\QRS',
	    'PoRFYF RZFaF RUM_M RSPaP Rf[l[ RWCWH R\\U\\Z R]C]H RSJSKaJ`L RfFfZ RlWl[ R\\U`Y R^RYW RjDcG R[PSS RZTSW RXR[T[WRZ R[[W[ RkKbL RlQbS',
	    'Po^KjK R_UiU R_XiX RX[k[ RWEWZ R_R_Y RbObQ ReHeM R_RiRiY R]IjHjM R_G^MjNkQ R^O\\Q RlXk[ RaD\\I R`EfEdH RfOfQ RZDRF RZKSL R[RRS',
	    'Po[G_G RTJXJ R[J_J RTMXM R[M_M RTQ`Q RUS_S RUW_W RUZ`Z Rf[l[ RTDTL RUOU[ RTDXDXL R[O[Z R[D[L R[D_D_L RfFf[ RlXl[ RUPRS RjDaF RUMUT RXGSG RkKaM RlRaS',
	    'PoUF^F RUI^I RSMXM R[M`M RSPXP R[P`P RSRaR RR[a[ Rf[k[ RSRSU RSKSP RUDUI RUKUP RSKXKXP RZDZI R[K[P R]K]P RUD^D_I R[K`K`P RfFf[ RkXk[ RWWZX RWU]UX[ RXTTX RjDbG RkKbM RlRbS',
	    'PoVNlN R`XRZ RiDUGUZ RaEaKbSeXh[k[lW',
	    'PoVMlM RTZbZ RVFVV RaQdXfZj[ RlWk[ R`E`JbT RhCWF R_URW',
	    'PoUKiK RUQlQ RUEUZ RUEiEiK R_YR[ R_LaScWfZk\\lX',
	    'PoVE]E RVJlJ RRTlT RVEVP R_Q_[ RlNkQ R`FbLeOjQ RgC^D RaNRQ',
	    'PoRI[I R]KjK R]PlP RRVZV RSISV RVCVI R]E]Z R]EjEjK RlWk[i[ RdXZZ RdLfX',
	    'PoWGkG RXJiJ RlXl[ RVIRM RXCTK RUNfNhZ',
	    'PoWFkF RXJiJ Rh[k[ RlXl[ RXDRJ R[UWYR[ R[QZW RTNgNgUhZ',
	    'PoWFkF RXIiI R^[c[ RhZk[ RlWl[ RWVS[ RXCWGRK RYOWW RUO`O_UcUc[ RULgLgThZ',
	    'PoWGkG RXJhJ Ri[k[ RaPZWR[ RXDVIRL RWR^UbZ RUNgNgTi[l[mX',
	    'PoWFkF RXJiJ Rh[k[ R_Q_[ RlXl[ RVWR[ RXDRJ RUNgNgUhZ RWQVX',
	    'PoWGkG RXIiI RUXbX RiZk[ RUQU[ R\\O\\X RbQbZ RlWl[ RVGRL RXCUI RUMgMgRhZ',
	    'PoWFkF RXJiJ R\\Q\\Z RbQb[ RXERJ RTZRZ RlXl[i[ RUNgNgThZ RVQVX',
	    'PoWFkF RXIiI R\\[`[ RZO_OeT RYWVZ RXCWGRK RWORT RlWl[ RZTXY RULgLgRhZ RVTaT`[',
	    'PoWGkG RWIiI RWTbT RWYbY Ri[l[ RWOWZ RWObObZ RlWl[ RXERK RUMgMgRhZ',
	    'PoWFkF RXIiI RXN\\N R_[c[ RUQU[ RUQcQc[ R[TWW RXCWGRK R\\P_V RlWl[ R\\PZV RULgLgRhZ',
	    'PoWFkF RXIiI RTOdO R_[c[ RiZk[ RURU[ RURcRc[ R]U`X R[U_V R[TXW RXCWGRK RlWl[ RULgLgRhZ R\\O[U',
	    'PoWFkF RXIiI RTScS RiZk[ RYMYW R^M^[ RTOcOcR RlWl[ RXCWGRK RWZT[ RTRSVdUdYaY RULgLgRhZ',
	    'PoWGkG RWIiI RUScS RUWcW Ri[l[ RUPUX R\\N\\[ RUPcPcX RlWl[ RWFRK RXCVH RUMgMgSiZ',
	    'PoWFjF RXIhI RYCSI RlXj[k[ RYOSQ RYV`W RWP\\SeT RVYb[ RWObP]RYTRU RSLgLgRiZ',
	    'PoVFjF RWIhI RUVcV RR[e[ R\\W\\[ RXCRI R\\ReT RUOaO\\RTT RlXk[i[ RSLhLhTiZ',
	    'PoWGkG RWIiI RSSdS R[N[[ RUOXQ RXERK RbO_R R[UcY R[TVXRZ RUMgMgRi[l[lX',
	    'PoWFkF RXIiI RWRaR RUZcZ RUOU[ RUOcOc[ RlWl[ R]U`X R\\TXX RXCWGRK R\\P[V RULgLgRhZ',
	    'PoWFkF RXIiI RTNdN R^VcV RUZcZ RiZk[ RUQU[ R_N_V RUQcQd[ RZNZUWW RXCVGRK RlWl[ RULgLgRhZ',
	    'PoWFkF RXHiH RUNbN RSQdQ RlWl[ RXCVGRK RZQWT RTSYU RaT]We[ R]STX R_VYYS[ RULgLgRhZ',
	    'PoWFkF RXIiI RTPdP RUTcT RSWeW R\\P\\[ RXMXO R`M^O RXCWGRK RlWl[ RULgLgRhZ',
	    'PoWFkF RXIiI RSSdS R[M[O RTOTQcOcR RXCWGRK RYWa[ RlWl[ RYQWW_Y R`U\\YT[ RULgLgRhZ',
	    'PoWFkF RXIiI RTOdO RRZfZ RZOZZ R_O_Z RlWl[ RXCWGRK RURWW RdRaW RULgLgRhZ',
	    'PoWFkF RXHhH RROdO RTUcU R^[e[ RTRTU R[M[R R^U^[ RTRcRcU RYCSI RXYR[ RlWkZiZ RSKhKhTiZ',
	    'PoWFkF RXIiI RUNcN RUVcV RSZeZ RiZk[ RaRbU R[R\\U RVRWU RbPaR RXCWGRK RVPUR R\\P[R RlWl[ R\\W\\Z RULgLgRhZ',
	    'PoWFkF RXIiI RSOeO R^WcW RRZfZ RURUWYWYZ R^RcRcW RXCWGRK RlWl[ R^O^R R^W^Z RULgLgRhZ RYOYRUR',
	    'PoWFjF RXHhH RSNeN RUScS RUVcV RiZlZ RUQUV R\\M\\[ RUQcQcV RlWlZ RYCSI R[VdZ RYVcX R\\VWYRZ RSKhKhTiY',
	    'PoWFkF RXIiI RiZk[ RlWl[ RbN_Q RbU_X RXCWGRK RVNUP RVUTX R[OcS R[R]W_Yd[ R[MZPXRTS R[SZWXYS[ RULgLgRhZ',
	    'PoWFkF RXHiH RVPbP RRSeS RUNbNbS RlWl[ RUUXV RcU_W RXCRI R\\VeZ R\\WSY R\\S\\[X[ RULgKgQhZ',
	    'PoVFjF RXHhH RSNcN RTPcP RRReR RUVbV RUYbY R_[b[ RUTU[ R\\L\\R RUTbTb[ RXCRI RlXk[i[ RSKhKhUiZ',
	    'PoWFkF RXHiH RVOcO RVQcQ RSSeS R`[c[ RVMVQ RVMcMcQ RlWl[ RaU[[ RXCWGRK RXSVVRW R[UXYTZ RUVdVc[ RUKgKgSiZ',
	    'PoVFlF RXIiI RVQcQ RVScS RRZfZ RUUUZ RVNVS RZUZZ R^U^Z RVNcNcS RUUcUdZ RlWl[ RWERJ RTKgKgSiZ',
	    'PoWFkF RXIiI RVSbS RRZeZ RiZk[ RUUUZ RVNVS RYUYZ R^U^Z RVNbNbS RUUcUcZ RlWl[ RXCWGRK R\\OYR R[P_Q RULgLgRhZ',
	    'PoY[_[ R_C_[ RjHbP R_HcRhWlX RSK\\KXSSX',
	    'Po^RU[ RVD]F RRLZM',
	    'PoY[_[ R_K_[ RjJeP RYRSX R`KbQhWlX RSM[MXT RVEgEdH_J',
	    'PoY[_[ R_C_[ RUFZH RjHbO RSN[NYSSY R_HaMdRjX',
	    'PoZ[_[ RVI_I_[ RYDdF RRO[OXTRZ R_JbRhXmY RjKfPcQ',
	    'Po_XbX RS[k[ RbDbW RbKjS R]PXU RjIeM RlWk[ RZJ_J[S RRFZFUORZ',
	    'PoZ[_[ R_H_[ RUKZP RcSfUhVkY RjJdP R\\SRX',
	    'Poe[j[ R`V[[ RVVTZ RTDYF RRKXM RXRUX RbQ^Y R[JlJlSj[ RcCbLaU',
	    'PoSG[G R[O_O R_C_O R_CaHeLlO R_PaUdXj[ R[IWMRO RjFbI R_P]UZXT[',
	    'PoZ[_[ R_J_[ R_KcT RhNbRgWlY R_DdIlL RSO\\OZSVWRY R_CZIRM',
	    'PodPjP R^[k[ R^E^Z R]EjEjP RTDYF RRLWM RlVk[ RXRTZ',
	    'Pog[l[ RZJgJh[ R]WY[ RTDYF RRKWM RlWl[ RXRUY R_R[Y R`C`L^U',
	    'Po[FlF R_[e[ ReFe[ RTDYF RRKWM RXRUY',
	    'PoZMlM RcCc[ RTDYF RRKWM RXRUY',
	    'PoSHkH RY[_[ R_C_[ RUKYP RdDiF R[SRX R_IcQhWlY RhMcP',
	    'PoTDYF RRKWM RXRUY R]RY[ R^FgFgKiX R^I\\V',
	    'PoeUk[ R_UY[ RcNgW RUDZF RRKWM RWRTY RaO]X RbDbKeU RbG`S',
	    'PoZElE R`[g[ RTDWF RRKWM RXRUY R_E]MjNh[',
	    'PoZ[_[ R_J_[ RZSTY R_LcT RhNbSgXkY RcHlL R\\DbG RSP\\OYV R^E[HRL',
	    'Po[EkE R[ZlZ R[E[Z RTDXF RRLWN RWSS[',
	    'Pod[j[ RUWT[ RgJ\\U RUDZF RRLXM RXSUY R\\FlFlQj[',
	    'PoTDYF RRKWM R^JbThYl[ RXRUX RiIeR_XY[ R]F`P R[FjFgP',
	    'PoaHdL RTDYF RRKWM R]IaSgYl[ RXRUY RiIeS_XZ[ R\\EjEgP',
	    'Po\\PhP Rd[j[ RTDYF RRKWM RWRTZ R`C[M R^IlHlSj[',
	    'PoeEeX RlDl[ RTDYF RRKWM RXRUYT[ R^D^Q\\X',
	    'Poh[l[ RZJhJh[ RlWl[ R]VY[ RTDYF R[OcS RRKWM RXRUY R_Q\\Y R`C`L^U',
	    'Po_LdQ RTDYF RRKWM RXRUY R]E]Q[X R]EgEhZkZmW',
	    'PoZKlK RfVk[ R_UY[ RdPgX RTDYF RRKWM RXRTY RaO]X RbCbKfV RbH`T',
	    'PoTDYF RRKXM R`IlIhRbX[[ RS[XR RbC_J[O R^MfS',
	    'Pob[k[ RbGb[ RlWk[ RTDYF RRLWM RXRUZ RiE[H RfOZQ RhO\\P RkN_P',
	    'Po\\IlI R][l[ R]I]Z RdCdI RTDYF RRKWM RXRUZ',
	    'PoZPlP R][c[ RcKc[ RTDYF RRKWN RXRUZ R[FkEgIcJ',
	    'Po]IlI R\\[k[ RdP[Y R\\NhNbS RlWk[ RTDYF RRKWM R_C[L RXRTZ',
	    'Po\\YlY R\\J\\[ RdDdX RlJl[ RTDYF RRKWM RXRTY',
	    'PoUJiJ RZ[_[ RUEUJ R_C_J R_K_[ RjEjJ R_LbSfWlY RSP[PYTUXRY RjNgQcS',
	    'Po[EkE RZNlN RcEc[ RTDYF RRKWM RXRUY',
	    'PoYNlN RcFc[ RTDYF RRKWM RXRUY RjEZG',
	    'Po[EkE RZNlN R^[c[ RcEc[ RTDXF RRKWM RWRS[',
	    'Po[EkE RZKlK Rb[h[ RTDXF RRKWM RWRS[ R`E^RjQh[',
	    'Po[OfO RlVk[ R^WZ[ RTDXF RRKWM RWRS[ RaE`R]Y R[EiEiUiZ RaL_V',
	    'Po]NjN R][k[ R]E]Z R]EjEjP RTDYF RRKWM RlWk[ RXRUZ',
	    'PoYKlK RTDXF RRKWM R\\SiY RWRS[ R`D[T RhKfQeUaX^ZY[',
	    'PoTEjE RRKlK RZ[_[ R_E_[ R_LbRgWlY RSP[PYTVXRY RjNgQcS',
	    'Po\\FjF R[ZlZ RcFcZ RTDXF RRKWM RXRTZ',
	    'Po][k[ R]F][ RcCcV RkIkT RTDYF RRKWM RlWk[ RXRUY RjIYN RjUfU',
	    'Po\\EjE RZLlL R`[g[ RTDYF RRKWM RXRTY R_L]RiRh[',
	    'Po[MkM RYZlZ RcDcZ RTDYF RRLWM RXRUY',
	    'PogVl[ RdPhX RTDYF RRKWM RXRUY RgJcS]YY[ R`C]N R_HgHdQ',
	    'Pof[j[ RaO[U R`Y\\[ ReS^Z RTDYF RRKWM RhLdU RXRUY RaL_Q R[EhEdH[LlLlRjZ',
	    'PoYHlH RcCcN RTDXF RRKWM R]O^ScWk[ RWRS[ RZNjNhRcVZ[',
	    'Po\\NkN R\\F\\[ RTDXF RRKWM RWRSZ RcY[[ RlWk[i[ ReEdLfX RiD\\F',
	    'PoZEbE RdElE RcOlO RhEh[ RTDXF RRKWM RWRS[ RbMYP R^E^Q]W',
	    'Po]OlO R]ZlZ R]E][ R]ElEl[ RTDYF RRKWM RXRUY',
	    'Po\\OhO R\\YlY R\\E\\[ R\\ElEl[ RTDYF RRKWM RXRUY',
	    'Po\\FlF R\\PkP RZZlZ RcFcZ RTDYF RRKWM RXSUY',
	    'PoZIkI RYQlQ RgCg[ RTDXF RRKWM RWRS[ R_C_R]X',
	    'PoTGjG RSKkK RZ[_[ R_P_[ RdCdP RZCZMVQ R_RdWkZ RSS[TWXRZ RiSbU',
	    'Poh[l[ R\\J\\[ R\\JlJl[ ReQjU RbQ^U RTDYF RRKWM RXRTZ R^DdDcNaS RdHeQ',
	    'Po]NgN R\\ShS Re[j[ RTDYF RRKXM RVUTY RXRUX R`C\\L R^HlHlSj[',
	    'PoYIlI R_ZkZ R]ZgZ RiUk[ RTDXF RRKWM RWRS[ R]PYZ R_I[W RcM_Z R`D\\T',
	    'PoZKlK RaVeZ RTDYF RRKWM RXRUY RcCcK`UY[ RcMfUl[',
	    'PoZKlK RgVl[ RfDjH R_UY[ RdNhX RTDYF RRKWM RXRUY RaP^X RbCbKeT RbG`T',
	    'PoSDXF RRKWM R^NcThYl[ RWRS[ RZEfEeL R^E^J\\SY[ R]MjMhTcX][',
	    'Po\\EkE RTDXF RRKWM R_LbTgYl[ RWRS[ R\\LjLgTbX\\[ R\\E\\PZX',
	    'PoZJlJ RcCc[ RVXSZ RTDYF RcNkR RRKXN RWTUX RXRWW',
	    'PobKeM RcDkL RcE[M RTDYF RRKWM R]QjQe[ RXRTY',
	    'PoYIlI RcCcI RVUTZ RSDXF RRKWM R\\I`SeXl[ RXRUX RiIeS`XY[',
	    'PoTDWE RRKVM R]J`RgXlZ RWRS[ R_D[N R^IiIeR_YZ[',
	    'PoZHlH Rc[h[ RcCcH R]VY[ RTDXF RRKWM RWRT[ R_Q[Y R_OjOjTh[ R`I^V',
	    'Po[YlY R[E[[ RlEl[ RbR]W R^HhQiT RTDYF RRKWM RXRTY RhFbS RhDeN',
	    'PoYPlP R[HiHiP RTDXF RRKWM RWRSZ RbDbPaT]YX[ RbPeViZl[',
	    'Po]HkH RZPlP RcHc[ RTDXF RRKWM RWRS[ R_C[L',
	    'Po]JkJ RZQlQ RcCc[ RUVS[ RTDYF RRKWM RXRUY R^E[N',
	    'Po]HlH R]KiK RTDXF RRLWM R^C[J RWRSZ R[OiOiVk\\mX',
	    'Pob[g[ RhIlM R_VZ[ R^EfEiL RSDXF RRKWM RWRS[ R^G[M RaN^W R\\NhNhVg[',
	    'Po[EjE RZNlN R^[b[ RcMc[ RkOgS R^HdJ RTDYF RRKWM RXRUY RiFbI',
	    'PoRJWJ Rb[e[ RYCY[ ReMe[ R`EjEeJ R]JZL_V RaHfJ R^MlMjR RWKTV RV[Y[V[',
	    'Po_[h[ R_I_[ RhMlT RTDXF RRKWM R_DeF RWRS[ RhWh[ R[MYV',
	    'Po\\MlM RfMf[ RTDYF RRKWM RXRUY R\\F\\SY[ R\\M[W RjD\\F',
	    'PoYNlN RTDXF RRKWM RXRUY RiDaF[F RbFbNaR_VZ[ RbNeUj[',
	    'Po\\FjF RZNlN RdYkY R`ZiZ R^ZgZ RZZdZ RfSiVk[ RTDYF RRKWM RXRUY R`N]Z',
	    'Po\\EjE RZMlM Rf[l[ RfMf[ R^VY[ RTDXF RRKWM RlVl[ RWRS[ R_N^W',
	    'PoZHlH Rh[l[ RcCcH R^MhMhZ RlWlZ RTDYF RRKWM RXRUY R^M^R]W',
	    'Po\\LkL RYLgL Rf[k[ RfOf[ RfGlL R]WY[ RTDXF RRKWM RlWl[ RXRUY R`C\\L R^O]X',
	    'Poc[k[ R[I[M RbCcMc[ R[IkIkM RlWk[ RTDXE RRKWM RXRUY RcIaQ^VZ[',
	    'Pog[l[ R[E[I R^KgKhZ R[EkEkI R]WY[ RTDYF RRKWM RlVl[ RWRTZ R^K^R]X',
	    'PobWeW RZ[k[ RSHSL RZNZZ RSH]H]L ReDeW RaP]T RkWlZ ReHhN RkIgKkT RWRS[ R_JcJ_S RXCXMVW',
	    'PoZJkJ Re[l[ ReNeZ RfEiH R^WZ[ RlWl[ RTDYF RRKWM RXRTZ R`Q]Y RbDbK`U',
	    'Po[TiT Rc[k[ R[K[V RcCc[ RjKjV RlXl[ RTDXF RRKWM RWRS[ RjFZH',
	    'PoZElE R\\ThT RY[l[ RTDXF RRKWM RWRS[ R^E\\U R^LiLhRg[',
	    'PodMlM Rd[k[ R^H^[ ReDe[ R^JYN RTDXF RRKWM RWRS[ R`D\\L RlWk[',
	    'Pof[j[ R]D]U RdV^[ RTDXF RRKWM ReQbY RWRSZ RbS\\U RaJYK RbElElUk[ RfEfMdV',
	    'PoZJlJ RcCc[ RgQlV R^QYW RcHgR RTDYF RRKWM RVUT[ RXRUY RbH^R',
	    'PoYZlZ R[EjEjO RTDXF RRKVM RWRS[ R[OiNiRiUhZ RaG`N^Z',
	    'PodNiN R[PjP RTDXF RRKWM R^RcWk[ RWRT[ R_C]L RiRdW_YY[ R_FjFiN',
	    'PoSH[H R[O_O RVUhU RVZhZ RVQV[ R_C_N RVQhQh[ R_DaHeLlO R[IWMRO RiGcJ',
	    'Po[ElE Rd[i[ RcEcR RcKkKkU RTDXF RRKWM RWRS[ R\\J\\SkRi[',
	    'Poe[i[ R^RYW RbW^[ R\\JXN RTDXF RRKWM RfNaY RWRSZ R_C[L RbI]S RgIcU R\\IlIlPj[',
	    'Po\\QlQ R\\I\\S RdCd[ R\\IlIlS RTDYF RRKXM RXRUY',
	    'PoUEiE RRJlJ R[[_[ R_N_[ RXP[S RfPcS RWPRR R[JUQ RbJhPlR RaUjY R^EZM R\\UUY',
	    'Po\\MbM Rd[k[ R\\D\\Z ReDe[ RTDXF RRKWM RWRS[ RiLdN RbYX[ RlWl[',
	    'Po^ScS RcCcR RiHkO RTDXF RRKWM RWRSZ R]HZO RiPgTdW_ZZ[',
	    'PocMkM RY[l[ R]J][ RcCc[ RTDYF RRLWM RXRTZ',
	    'PoYHlH RfWjW R[M[W RcCc[ R[MjMjW RSDXF RRKWM RWRTY',
	    'Po]JlJ ReCeJ R]D]RgQg[ RTDXF RRKWM RWRS[ R\\TY[ R]M[X',
	    'PoRKWK R`[d[ RdDd[ R^IZL]S RkIfMkW RVPRX RbO^W RdGhS RWLTW R^KbK_U RYCY[U[',
	    'Po[ElE RfWjW R\\J\\W RcEc[ R\\JjJjW RTDYF RRKXM RXRUX',
	    'Po_UfU R]UdU RZUaU Re[j[ RdQgV RTDYF RRKWM RWRS[ R_C[M R`L]U R]HlHlUj[',
	    'PobFb[ RTDXF RRLWM RWRS[ RhEhMjX R\\F\\PZX RjD_F',
	    'PoTDXF RRKWM R]P_ScXj[ RWRS[ R^E\\MYN R^EgEgMkMlJ RZPjPdW^ZY[',
	    'Po]NjN R]EjEjN RSDXF RRKWM RWRS[ R]E]N\\TY[ RbOfUk[',
	    'Po[HkH R\\NkN RZTlT RcCc[ RTDYF RRKWN RXRTY',
	    'Po[EkE R[[l[ R[E[[ R^JeQ^W RTDXF RRKWM RhHeQ RWSS[',
	    'Po[ElE Re[i[ RSDXF RRLWM RWSSZ R_Z][ R^LkLkRiZ R[E[QYZ RcGcNaW',
	    'Po^[i[ R^M^[ RbDiLlM RjXi[ RaEZM RTDYF RRLWN RWSTZ RhO]T',
	    'PobUgU R][j[ R]M]Z RjWj[ RbDjM RbEZM RTDXF RRLWN RXRUZ R]MhMgU',
	    'PolXl[ R`U]X R_J`MbOfX RTDXF RRKWM RWRS[ ReI_W R[EiEiTjZ R[E[RYZ',
	    'Pod[j[ RdPhT R\\E_H R_VZ[ RTDYF RXRUY RaO]X RRLWM RZKlKlSj[ RcCbJ`T',
	    'Po[ElE R\\RkR R\\JkJkS RTDXF RRKWM RWRTZ R\\J\\R[X',
	    'PoZHlH RZOkO RcCc[ RTDXF RRKWM RXRTZ RcO`SYZ RcOfTlY',
	    'Po[HjH RZOkO RbCb[ RdRlY R`SYY RTDYF RRKXM RXRTZ RbM`S',
	    'PoYIlI RhYlY RbCb[ RiLiY RlUlY RTDXF RRKWM RfDjF RWRS[ R]N\\W',
	    'Po]LiL R]SiS RY[l[ R]E][ R]EiEi[ RTDYF RRLWM RXRTZ',
	    'PoSG[G RSRkR RYZhZ RYTY[ RXVhVh[ R_CaHeLlO R[IWMRO RjFbI R[RXVRX R_C_O[P',
	    'Po[ElE R^ZjZ R^M^[ R]OjOj[ R^NYS RTDXF RRKWM RWSS[ RaE\\Q',
	    'Po_[k[ R[H[K R_K_[ RlWk[ R[HlHlK RTDYF RRLXM RUWT[ RXRUY RdDcH RhP^S',
	    'Po\\HlH RgUiU R^[k[ R^K^[ RcIcW RjLjU RlXk[ RTDXF RRKWM R^C[K RWRS[ RiLYQ',
	    'Po\\TdT R\\K\\V R\\KdKdS RTDYF RRLWN RXRUY R[EmE RjEj[d[',
	    'PobCiKlL RbDZL RTDXF RRKWM RWRS[ RcK`N[P ReOcR\\U RiSeWaYZ[',
	    'PohOlW RTDYF RRKWM RXRUY R^O[W R]IlIjM R^C\\L RdIc[`[',
	    'Po\\EjE RZLlL R_[c[ RcLc[ RhQlX RTDXF RRKWM RWRS[ R]QZW',
	    'PoZIlI RfDiG R\\VXZ RTDYF RRKWM R`C`NbTgYl[ RXRUY R_N[Y RiNfTbY][ R`G^T',
	    'Po[NkN ReCe[ R[GkGkM RTDXF RRKWM RWRS[ RkXhX R\\MZSlRkX RaDaQ_W[[',
	    'Po\\QlQ R\\ZlZ R\\I\\[ RdCdY R\\IlIl[ RTDYF RRKWM RWSUY',
	    'Po\\OlO R\\ZlZ R\\E\\[ RdEdY R\\ElEl[ RTDYF RRKWM RXRTZ',
	    'Po[MkM R\\ZiZ R\\Q\\[ R\\QiQi[ RfHlN RTDYF RRKWM RXRUY RaC\\M',
	    'PofNkN R\\ZlZ R\\Q\\[ R\\QlQl[ RTDYF RRKWM RcEaK^NZO RXRUXTZ R[ElEkN',
	    'PoZJlJ R\\ZjZ R\\Q\\[ RcCcQ R\\QjQj[ RTDYF RRKWM RXRUY',
	    'PobJlJ R[ZjZ R[P[[ RbCbP R[PjPj[ RTDYF RRKWM RXRUY',
	    'Po\\ZiZ R\\Q\\[ R\\QiQi[ RTDYF RRKWM R_D[M RXRTZ RfDiKlN',
	    'Po[IhI R\\UfU Rf[l[ R\\N\\W R\\NfNfU R[ElEl[ RTDYF RRKWM RWRS[',
	    'Po\\NjN Rf[k[ R\\E\\N RfNf[ R\\EjEjN RlWl[ RTDYF RRLXM RS[XS R`O^VZ[',
	    'Po`SgS Rh[l[ R[E[[ R`K`U R`KgKgS R[ElEl[ RTDYF RRKWM RXRTZ',
	    'Po^UfU Rd[j[ R^M^W R^MfMfU RTDYF RRKWM RXRUY R`C]K R^HlHlRj[',
	    'PoYKlK RbShS R\\ZlZ R\\D\\[ RbCbU RhDhU RTDXF RRKWM RWRS[',
	    'Po\\ZlZ R\\E\\[ R\\ElEl[ RfRjW RbR^W RdNhU RTDYF RRKWM RWRTZ RcN`U RdJfS RdIcR',
	    'Po\\IjI RYPlP RdUjZ R]YY[ RaU[Z RbCbPfW RTDXF RRKWM RWRS[ RbP_W R]D[M RbKaT',
	    'PoeXlX R][a[ ReGeZ ReGlGlZ RTDXF RRKVM RWRSZ RYIbIbWa[ R]C]N[X',
	    'Po_FYK RTDYF R]HbNkR RRKWM RWSS[ R_C]I R^GhGbNYR R_SgU R\\Wg[',
	    'PoVJhJ RVOhO RZ[_[ RVFVO R_O_[ RVFhFhO R^C^F R_OaSeWlZ RgRcT RSR[RWXRZ',
	    'Po\\QlQ R\\ZlZ R\\H\\[ R\\HlHl[ RTDYF RRKWM RXRTY RcDaH',
	    'Po[HkH RZQlQ RcCcL RgLg[ RTDXF RRKWM RWRS[ R[YYZ R^L^R]W',
	    'Po_[h[ R`I`[ RiWh[ RhMlT RTDXF RRKWM R_DdF RVSS[ R[MYU RjEdQZ[',
	    'PoYJlJ R]UgU RbCb[ RfQlV R^QXV RcJhS RTDYF RRKWM RWRS[ RaJ]R RbH^P',
	    'Po^IlI RbNlN RbTlT RbHb[ R]JZN RTDXF RRKWM RWRS[ R`D\\L',
	    'Po\\NkN R[YlY R[R[[ R\\F\\N RdCdY RkFkN RlRl[ RTDYF RRKWM RXRTZ',
	    'Pof[j[ RZEZ[ RSDXF RRKWM ReQaZ RVRSZ RZE`E\\L R^M`Q`T R_U\\V RaJlJlTj[ RfCfNdV',
	    'PoYK^P RTDXF RRKWM ReRbZ RVRSZ R^D[L R]QZW R`K\\U Rj[g[ RbIlIlTj[ RfCfLdV R`RaVYW',
	    'PoeSgZ RTDXF RRKWM RWRS[ RhDhLjY R[E[PYX RbE[E RiD^E RaGbX\\Y RfX_Y',
	    'Po[K`K RZQ`Q RZ[_[ RZD`D`L RTDXF RRKWM RVSSZ RiQl[ RZKZR R`R_[ RfEeMcY RkXaZ',
	    'PoZIlI R]QhQ R]YhY R]C][ RhCh[ RTDYF RRKWM RXRUY',
	    'Po[HjH RZOlO RcCcO RTDYF RRKXM RXRTZ RaP\\ZjX RhUkZ',
	    'PohUlU R[F[T ReFe[ ReFlFlU R`V[[ RTDXF RRKWM RWSS[ RbC[G RaGaTXT RaN`W',
	    'PofPlP R[YlY R[E[[ RaEaO RfEfP R[ElEl[ RTDYF RRKWM RXRTZ R_R\\R',
	    'PoZQlQ R^[c[ RcEc[ RTDXF RRKWM RjHgO RWRS[ R\\I^O R`F[F RiD_F',
	    'PoZEkE RYQlQ RcEc[ RiJkN RgHjK RTDXF RRKWM RWRS[ R]H[N',
	    'Po^LcL Re[k[ RZIZZ R^C^Y ReCe[ RTDXF RRKWM RWSS[ RjKeN RlWk[ RcYXZ',
	    'PoZLiL R`[l[ RbFbL R`W\\X RhN_W RTDXF RRKWM RWRS[ R[VY[ R]YeZ RjDZF',
	    'Po\\MlM RYZeZ R\\F\\W RTDXF RRKWM RWSS[ RlVkZiZ RjD\\F ReEeKgX RdUYW',
	    'Po]LlL ReLe[ RTDYF R`PkU RRKWM RXRUY R]F]TY[ RjD]F R]N[X',
	    'PoZNlN R][c[ RZGZK RcNc[ RZGlGlK RbCcF RSDXF RRLWM RWRS[',
	    'Po[FlF RcOkO RYZlZ R\\L\\Z RcFcZ RTDXF RRKWM RWRS[',
	    'Po]LgL ReXiX RaPa[ RZPiPiX RbCjL RaDZL RTDXF RRKWM RWRS[',
	    'Po\\QeQ R][k[ R]Q][ R\\KeKeQ RlWk[ RTDXF RRKWM R_C[K RWRS[ R]GkGkOjUeU',
	    'PocCcN RTDXF RRKWM R^N`TdXl[ R[HkHiL RWRS[ R[NiNgTbX[[ R[H[QZX',
	    'PoZIkI RX[l[ RbCbI RTDYF RRKWM RWRS[ RhKd[ R]L_X',
	    'Po\\E\\X RfVa[ R`FcM RhQlZ RTDXF RRLVM RhPdY RWRS[ R`U[W RiDiHgU',
	    'Po\\KjK R`[k[ RaNa[ R\\EjEjK RlWk[ RTDYF RRKWM RWSTY RjO`T R\\E\\R[Y',
	    'Po\\KjK RhZlZ R^PhPhZ R\\EjEjK RlWlZ RTDWE R`V\\[ RRKVM RWSS[ R\\E\\OZX RbMaW',
	    'Po[IlI R[I[V RgDjG RaY][ RfS_Z RTDXF RRKWM RiLeU RWRSZ RbSYW RlXk[i[ RdCdJfW',
	    'PoZIkI R[QjQ RY[l[ RcIc[ RTDYF RRLWM R_DdF RXRTZ',
	    'Po]JkJ R\\QjQ RZ[l[ RcDcZ RTDYF RRLWM RXRTYT[ R^E[N',
	    'Po]KlK R]RlR R]ZlZ R]E][ R]ElEl[ RTDYF RRKXM RUVTZ RXRVW',
	    'PoZHlH RZZkZ RgUkY RePbS R[N^PbT\\Z R_L^O RTDXF RRKWM RgKfP RWRS[ RcCbH_M',
	    'PoZHZL RcCcH RZHkHkL RgRkZ RSDXF RRKWM RWRS[ R^Q[Z R_MfMiX',
	    'Po_JlJ Rd[h[ R]H][ RiCi[ RbNdT RTDXF RRKWM RWRS[ R_CZN',
	    'Po\\MjM RZSlS RcCc[ RfFkJ R^GZK RTDXF RRKWM RWRS[',
	    'Po\\KkK R\\QlQ R\\E\\Z R\\EkEkK RlWk[ RTDXF RRKWM RgZk[ RWRS[ RdKfX RdYYZ',
	    'PoTFjF RUJiJ RROlO R[[_[ R_P_[ RXR[U RfRcU RbKhQlS RbWj[ R^D[LURRS R\\WTY',
	    'PoXPlP R[H[P R[HiHiP RTDXF RRKVM RbCbQeViYl[ RWRSZ RaT\\YX[ RbLaU',
	    'PogXkX RdCd[ RbR[Y RTDYF RRKWM RXRTZ RcNaS R\\J[QlPkX RkE[H',
	    'Po^[b[ R\\IbIc[ RkKfP RTDXF RRKWM RWRS[ R^DfF RZO_O]UXY RcKfSlY',
	    'Po[Q_Q RR[l[ RVSV[ R\\S\\[ R_C_Q RbSb[ RVShSi[ R_DaJfNlP RSH[GZKVORP RiEgHbJ',
	    'PoSEkE RYMhM RSR\\R R[[_[ RYHYM R_O_[ RWIhIhM R_QeWlZ R[SWXRZ RhScU R\\EXHRK',
	    'PoZ[_[ RSISM R_K_[ RSIkIkM RUDXG RhDdI R_LcT RiOcSgWlY RSP\\PZTVXRY R^C_G',
	    'PoZJlJ Rd[l[ ReJe[ RlXl[ ReEhF R\\WX[ RiQaY RTDYF RRKWM RjNhR RXRUY R^Q[Y R`D`J^U',
	    'PocFlF R\\QkQ RcCcJ R\\KkKkS RTDYF RRKWN RWSTY R\\K\\S[Z',
	    'Po^MjM R^UjU R[ZlZ R^F^U R^FjFjU RTDYF RRKWM RXRUZ',
	    'Po\\EeE RZNkN R`[c[ RcHc[ RSDWF RiRlY RRLVM RVRSZ R]RZX R\\F[N RiD_E',
	    'Po^KiK RYTlT R`[e[ RZEZH RZEkElH RTDXF RRKWM RWRS[ R_G]PgPf[',
	    'PoZJkJ RUDXG RRKVM RgEiG R]SXX R^PfXl[ R]E[J RWSSZ RaJ\\U R_OiOgSbX\\[ RbD^Q',
	    'Po[RjR RYVlV RcOc[ RTDXF RRKWM RWRSZ R\\EbKgMlN R[EiEeJ_MYN',
	    'Po\\QjQ RZ[l[ RcQc[ R[EiEcKlO RTDYF RRKWM RXRTZ RdJZO',
	    'Po[JiJ R[OiO R[E[[ R[EiEiO RTDXF RRKWM RaOdUgYl[ RWRS[ RjRdU RbYZZ',
	    'PoYOlO Re[i[ RbCaG RcQcY R]GiGi[ RaIcL RTDXF RRKWM RWRS[ R]G]R[Y',
	    'PoZHlH R[NkN R\\ZjZ R\\R\\[ RcCcN R\\RjRj[ RTDWF RRKWM RWSUY',
	    'PoZHlH Rb[f[ RfHf[ R_VZ[ RbCdG RiMlT RTDXF RRKWM RWRTY RaH`R]Y R\\MZU RaN_W',
	    'Po[GlG RfVj[ ReJeShY RTDYF RRKWM RkLhR RVUT[ RXRUY R_K[S RdVaY][ R`F]Q R`M`R R`C^O ReMdW',
	    'Po`TgT R[YlY R[E[[ R`K`T R`KgKgT R[ElEl[ RTDYF RRKWM RXRUX',
	    'Po[EkE R\\NjN RYTlT Rf[j[ R\\I\\[ RcEcS R\\IjIj[ RTDXF RRLVM RVSSZ',
	    'Po^WbW RYZlZ RbJbW RTDWG RSLUN RdOkV R]QYU RhMeN RjJgM RbJeP RVTS[ RZK_K\\T R[EiEfGbI',
	    'Po^LjL R[YlY R[E[[ R[ElEl[ ReRiV RbR^V RTDYF RRKWM RXRTZ RdM`U RcHdNbS',
	    'Po[KlK ReUiU Ra[k[ RaNaZ RaNiNiU RlWk[ R]HYL RTDXF RRKWM RWRS[ R^FfFdJ R_C\\J\\S[Y',
	    'Po]EcE R]KlK R`YjY R`P`[ R`PjPj[ RTDYF RRKWM RXRUY R]E]S[Y RjD`E',
	    'PoZGlG R`SlS R]L][ R^LYQ RTDXF RRKWM RWRS[ RaC]N RaLkLfPf[b[',
	    'Po[HkH R\\NjN RZTlT RcHc[ R]C_G RhCfG RTDXF RRKWM RWRS[',
	    'PoZEdE Rh[l[ RfGfU RlCl[ R[N`S R_UY[ RTDXF RRKWM RVSSZ R]HYQ RbO^X R]EZP R\\LbK_U',
	    'Po\\EkE R`SjS R\\ZlZ R\\E\\Z R`K`R ReEeK ReSeZ R`KjKjR RTDXF RRKWM RWST[',
	    'Po]MlM R]SlS R]ZlZ R]G][ R]GlGl[ RTDYF RRKWM RXRUY RdCbG',
	    'PoYElE Rh[k[ R[K[[ R`K`[ RfKf[ R[KkKk[ RTDXF RRKWM RWRS[ RbE`M',
	    'PobEkE RaLlL Rc[h[ R^L^[ RhLh[ R^EZI R^MYQ RTDXF RRKWM RWRS[ R_I]N',
	    'Po_LkL R]H][ RhEkG RgRjZ RTDXF RRKWM RdR`Z RWRSZ R_CZN ReCfNiX ReIhV ReIcV',
	    'PoYElE ReRkR R[YkY R[K[[ RfEfR R[KkKk[ RTDXF RRKWM RWRSZ R`E`M_Q',
	    'PoZGkG RgRjR R[K[R RcCc[ R[KjKjR RcQjY R]XYY RcQ[Y RTDWF RRKWM RWRS[',
	    'Po[GjG RYMlM RZQlQ Rb[g[ RcCcM RgMg[ R]T`X RTDXF RRKWM RWRS[',
	    'Po\\PhP R\\ThT Rd[h[ R\\K\\[ R\\KhKh[ RfFlL R]HXM ReCgH RTDXF RRKWM RWRS[ R_D\\J',
	    'Po\\KjK R[OkO RYTlT R\\E\\K R\\EjEjK RTDXF RRKVM RbSdVgYl[ RWRSZ RaV_YY[ RcOaW',
	    'Po\\IkI RYOlO Re[k[ RcCcO RfOf[ RlWk[ RTDXF RRKWM RWRSZ R]EZL R_O^WZ[',
	    'Po]GhG R[LlL R`PkP Rc[h[ RbCbL RTDXF RRKVM RWRSZ RbOYS RjFaO_UiUh[',
	    'Po]IjI RZOlO RcCc[ RfTlY R`SYZ RdOgU RTDYF RRKWM RWRS[ RbO_T RcMaR R^D[M',
	    'Po[QiQ R[WkW R[S[X RcNc[ R`C_FZK RTDXF R^GcLjO RRKWM RWRS[ R_FhFcLZP',
	    'Po^GhG R\\ZiZ R\\R\\[ R\\RiRi[ RSDXF R^HcNlR RRKWM RVSS[ RgHbNYR R_C^HYL',
	    'PoWPgP R[[_[ RWLWP R_R_[ RWLgLgP R_ScWj[ RTT\\TXXRZ RgTbV R[DTG RZGbJlL RXFfFaH\\JRL',
	    'PoYNlN RaDcF RSDXF RRLWM R\\SkZ RWSS[ RaI\\S RhNeUbX[[ RZGZK RZGkGkK',
	    'Po^IiI R`UgU Rg[l[ R[D[[ R`M`V R`MgMgT R[DlDl[ RTDYF RRKWM RXRUY',
	    'PoZGlG R\\OjO R[JjJjN RTDXF RRKWM RbCbReWgYl[ RWRS[ RaU]YY[ R[NZSkRjVgV RbMaU',
	    'Po_JjJ R\\JhJ R\\PkP RZUlU RcKc[ RfEkJ RbC\\J RTDXF RRKVM RWRSZ R^L[S',
	    'Po[GjG R[KjK RZPkP RbCb[ RTDXF RRKVM RbPhWlX RWRS[ RbP\\VXX',
	    'Po_KlK R`RjR R]G][ RaEjEjS RSDWE RRKVM RVRSZ R^CYO ReEeRdX',
	    'PoZNlN RbCb[ RfRlX R_RYY R[F^K RdNfT RTDXF RRKWM RWRS[ RaN_S RbL`Q RiEgL',
	    'Po\\NiN R\\TiT RZ[k[ RbNb[ R\\EcEiKlL RaFZM RTDXF RRKWM RWRS[',
	    'PoYKlK R[OiO RZRjR RXWlW RbCb[ R[GiGiO RUDXG RRLUM RWRSZ',
	    'Po[ElE R]RjR R]ZjZ R]K][ R]KjKj[ RTDXF RRKWM RWRTZ RcEbK',
	    'PoZGlG R^PjP R^UjU Rf[j[ R_J_[ R]MjLj[ R^KZP RTDXF RRKWM RWSUY RaC]N',
	    'PoZHlH RcCcG RfJkO R_JZO RSDWF RRKWM R_OaTeXl[ RWRS[ RfNdT`XY[',
	    'Po[LjL R[QjQ R[G[R R[GjGjQ RTDXF RRKWM RWRS[ RhSeV`YY[ RcDcQdWgZk\\lW',
	    'PoZIlI RZRlR R_C_R RgCgR RfVl[ R_VZ[ RTDYF RRKWM RWRTY',
	    'PoX[l[ R[H[[ R`H`[ ReHe[ R[HjHj[ RTDXF RRKWM RWSS[ RbC`I',
	    'Po]IgI R_QcR RcV^[ RSDXF RRKWM RePaX RlWl[ RVSSZ RaI^S R`MfMcV R[DiDiVj[ R[D[QYX',
	    'Po[ElE R^IjI R_OjO R^VkV R[ZlZ R[E[Z RdIdU RTDYF RRKWM RXRTZ',
	    'Poe[l[ ReCe[ RlXk[ RZH^K RjGfK R^VY[ RTDXF RePjR RRKWM RWRSZ R_PYS R_C_Q^W',
	    'Po_EkE ReNiN RSR[R R[[_[ RYDYJYO R_O_[ RcJ_N R`OaSeVlY R[SWXRZ RiPgScT RdEbL R]ERG R]HSJ R^LRN RkFjN',
	    'Po]ElE R]TjT R][l[ R]E][ R]KjKjT RTDXF RRLVM RWRS[',
	    'Po[ElE R^KhK R^PhP R^E^W RhEh[ RTDYF RRKWM RXRUY RbVZW ReV]W RhUaV RkUdV',
	    'PoeDe[ RlDl[ RgKjQ RTDXF RRKWM RWRSZ R[KZQ RaKbQ R^D^Q]X',
	    'PoeYlY ReFe[ ReFlFl[ R]TbY RZS^T R_UY[ RTDXF RRKWM RVSSZ RaP]X R]CZT RYIbI_V',
	    'PoZIbI RcIlI RcQlQ RgHg[ RTDXF RRKWM RWRS[ RiCfI RbPYR R`C]I^R]X',
	    'Po]QfQ R]VfV Rd[j[ R]L]X R]LfLfV RTDXF RRKWM RWRSZ R_C[L R]HlHlTj[',
	    'Po\\VgV Rd[j[ R\\M\\X RgMgX R^LdR RbP^T RTDYF RRKWM R_C[L RWRS[ RdJ`S R]HlHlTj[',
	    'Po[EkE R[MhM R[SjS RYZlZ RcNcZ RiKjN RfHiK RaF[M RTDYF RRKWM RXRTZ',
	    'PoYNlN Re[l[ RbCbN RfNf[ RlWl[ R[E]K RTDXF RRKWM RWRS[ RiEfK R_N^WZ[',
	    'Po[EkE R]PiP R]ViV RY[l[ R]J]V R]JiJiV RTDXF RRKWM RWRS[',
	    'Po_GjG R_ZlZ R`Q`[ R]RlRl[ RiHaP RaDZK RTDYF RRKWM RWRS[ R]IaKdNZS',
	    'PoZKlK R\\ZjZ R\\R\\[ RdEdR R\\RjRj[ RTDYF RRKWM RXRUY RjD]E',
	    'Po\\HjH RZNkN R[SkS RYZlZ RcCcN RcOcZ RTDYF RRLWN RXRUY',
	    'Po]LhL R\\ZiZ R\\Q\\[ R\\QiQi[ RbCkL RbEZL RTDXF RRKWM RWRS[',
	    'PoaLa[ RTDXF RRKWM RWRTZ RkJ]L RjD[F\\QZX RdLgUl[ RkNgQ',
	    'PoZGlG R_MgM R[PjP Rb[f[ ReIkN R`IZO RTDXF RRKWM RWRT[ RbC^L R^P^UhTg[',
	    'Po\\QlQ R\\ZlZ R\\I\\[ RaCaY RfCfY R\\IlIl[ RTDXF RRKWM RWRS[',
	    'PoZGlG R[NjN Rh[l[ RbPbZ RiPi[ RlWl[ RfJkO RTDXF RRKWM RWRS[ RbCaG^M R]Q[YY[',
	    'Po[JjJ RZQlQ ReSkY R]D_H RTDXF RRKWM RWRTZ RhCeI RaU^YY[ RbJbPaU',
	    'PoZHkH RYQkQ RUDWG R\\J]O RRKVM RbCbQdVhYl[ RiJfO RWRSZ RaU]YX[ RbLaU',
	    'PoYOlO R[TiT R^[b[ RbJb[ RZKiJiU RTDXF RRKWM RWRS[ R_DYK R^EgEcJ',
	    'PoTDXF RRKWM ReDiE RWRS[ R[IhH R[MiL R[RjQ RaDaJcSeXh[k[mW RhSfW`ZZ[',
	    'Po[[_[ RZCZO R_P_[ RTEVI RaIdL RcC]I RTS[SWWRY RiQbU RYJRM R_RcWgXlY RaFkFgKcM\\N',
	    'PodGjG RaGhG R_GeG R[HaH RZRlR Rf[l[ RfRf[ RlXl[ R_W\\Z RlLkO RTDXF R`EcKfNjO RRLWN RWRTY R`CbK R`R^Y RhJcL[N',
	    'PobFkF R\\K\\U RbCbK R\\KjKjU RTDYG RRKWN ReWl[ RWSTZ RbU_YZ[ RcPbW',
	    'PoaElE RiWkW RZGZQ RbJbX RfEf[ RbJkJkW RSDXF RRKVM RWRS[ R^D^P]W',
	    'Po[RjR R[I[R R[IjIjR RiTl[ RTDXF RRLWM RXRTY RbCbY[Z RgYcY_Z RjXcY',
	    'Poh[l[ RZEZU RZEcEcU RhFhU RlDl[ R`Wd[ R^WY[ RTDXF RRKWM RVSS[ R_H_R^W',
	    'Po\\G\\[ RfDiH RgS_[ RlWk[ RTDXF RRKVM RfZj[ RiMfT R]CYN RWRSZ RcCcLdW RkJgKcK^L RfK`K',
	    'Po^MgM RZRkR RcZiZ R[ZcZ RfUjY R_T^X[Z RbEZL RTDYF RRKWM RbDhJlL RXRUY RaR^W',
	    'PoZGlG RgQg[ R]WY[ RaCbG RTDXF R^HbMiP RRLVM RVRSZ RgGfJbNlP RcNZP R^Q]X',
	    'Poh[l[ RgFgU RlCl[ RTDWG RRLVM R^D_G R]UYZ R[M_QbW RVSSZ RaN\\X RZHcH^U',
	    'Po[GlG R[LlL RcCdF R_H`K RTDXF RRKWM RWSSZ RgHfL R[L[SZY',
	    'Po\\KjK R\\RjR RZVlV RcMc[ RZEkEkI RTDXF RRKWM RWRS[ RaH\\Q RZEZI',
	    'PobHkH R`PlP RXM]M]Y RgHg[ RSDVF RRKVM RZD]G R`W\\Y RVRRZ RcCaL',
	    'PoZHZK R^O^[ RZHkHkK RfUlZ RjOgR R_NYT RdNhW RTDXF RRLVM RWSS[ RbG^P RcIfU RcD`M RcY]Z RbGeS',
	    'Po\\IjI R[NjN RZSlS Rb[g[ RhPh[ R[EjEjN R]U`X RTDXF RRKWM RWSS[',
	    'Po\\KiK R\\EiEiK R`PcR RhRlV ReKiT RTDWF RRKWM R^VdY RWRS[ R\\E\\M[T',
	    'PoZQlQ Rc[h[ RhNh[ R^SaW R\\H^L RbGcL RTDXF RRKWM RjFhL RWRS[ RjD_E',
	    'PoYIlI R^[b[ RbCb[ R[L]O RfSkX RfDiE RkLfQ RbKgU RTDXF RRKWM RWRS[ R`TXW RbHeR',
	    'Po[JkJ Rc[k[ RcMc[ R[EkEkJ RkXk[ RTDXF RRKWM RWRS[ RhL]M R[E[PZX RjP]R RlT]V',
	    'PoYIaI RcLlL RY[][ R]C][ RiLi[ RTDWF RRKWM RVSSZ RaOXR RkDcFcRbY',
	    'Po[IjI RYIhI RfFkJ R`PZV RTDXF RRKWM RWRS[ R`C^I R`I^NYP ReIeOkNkO R^SbWgZl[ R^RhRdW`YY[',
	    'Po^JgJ R\\ZiZ R\\S\\[ R\\SiSi[ RcChIlJ R]IYK RbC\\J RTDXF RRKWM R\\NhMfR RWRS[',
	    'Po\\KkK RYTlT R\\F\\T RgKgT RgWk[ R^VZ[ RTDXF RRKWM RWRS[ RiD\\F',
	    'PoZJlJ RbZkZ RbJbZ ReEgH R\\VW[ RlVkZ RTDXF RRKWM RWSTZ R^QZX RhLaO RiPaT RjTdW]Y R_C_L]U',
	    'Po\\LjL RcRjR Rc[l[ R\\E\\L RcLcZ R\\EjEjL RTDYF RRKWM RWRS[ R]QZY R\\S^WaYhZ',
	    'Po[I[[ R_H_W RTDWF RgVl[ RRKVM RfT`[ RdOhX R^CYM RVSSZ ReC`N RcIfV RcIkIfU',
	    'PoYLlL R]P]Y RcNc[ RTDXF R_MYT RRKWM RWRSZ RaI^P R\\DiH RiDaGZI R\\RjQjXgX',
	    'Po[FkF R]OhO RYTlT RZKZN RbCbK RZKkKkN RTDXF RRKWM RWRS[ RgPbRb[^[',
	    'Po[ElE R\\QjQ RY[l[ R\\I\\Q R\\IjIjQ R^S`Y RTDXF RRKWM RhSd[ RXRTZ',
	    'Po\\LjL RZQlQ Re[l[ RZGZJ RcCcG RfQf[ RZGkGkJ RlWl[ RTDXF RRKWM RWRS[ R_W\\ZY[ R`Q^X',
	    'PoZNlN R_ZkZ RZGZJ RcCcG RZGkGkJ RgUjWk[ RTDXF RRKWM RWRS[ R_OZZ R`I\\W RcQaZ',
	    'Po]JiJ R\\SjS R\\[k[ R\\M\\[ R]D]J RcMcS R]DiDiJ R\\MjMjT RlWk[ RTDXF RRKWM RWRS[',
	    'PoZGlG R[PjP R[UjU Rf[j[ R[L[[ RcCc[ R[LjLj[ RTDXF RRKWM RfDkE RWRS[',
	    'Po\\LjL R[UkU RY[l[ R\\E\\L R\\QcPc[ R\\EjEjL RTDXF RRKWM RWRS[ RjO`P',
	    'PoZNkN RZGZK RbIb[ RZGkGkK RdQlY RbCcG R`RYY RbMeS RTDXF RRKVM RWRSZ RbM_T',
	    'Po\\IkI RZNlN R]ZjZ R]R][ RdCdN R]RjRj[ RTDYF RRKWM R^DZL RXRUY',
	    'Po[KiK R[QiQ R[F[Z RbCbF R[FiFiP RTDXF RRKWM RaQcUgYl[ RWRS[ RjSdV RcYYZ',
	    'Po\\NjN RZTlT R[E[I RcNc[ R[EkEkH RgHkL R`H[L RTDXF RRKWM RWRSZ',
	    'Po[JjJ R[PjP R[UkU RY[l[ R[E[P RcEc[ R[EjEjP RTDXF RRKWM RWRS[',
	    'PoZEkE RZMjM RZRjR RZIZR RZIjIjR RTDXF RRKWM RWRSZ RaU_YY[ R]T`XdZl[ RbEbQaV',
	    'PoZSlS R^[c[ RcQc[ RiGhK R[H]K RTDXF RRKWM RWRS[ R[MjMcQ RiD^F RaFcJ',
	    'Po[DkD RYOlO R\\ZjZ R\\S\\[ R[IhIhO R\\SjSj[ RTDXF RRKWM RWRT[ RaD^O',
	    'PoZKdK Rh[l[ R`E`[ RfFfU RlCl[ R_MdR RTDXF RRKWM RVSSZ R_K[U RcDZF',
	    'Po[EkE R^IjI R[NlN R_N_[ RfTlZ RiRfS RcNgV RTDXF RRKVM RWRSZ RdX^[ R[E[QYY',
	    'Po\\PjP R\\TiT R[WkW RcNc[ RaCZJ R_GbKkO RTDXF RRKWM RWRS[ R_GhFcK^NYO',
	    'Po]MkM R]QhQ R]UhU Rd[h[ RiMi[ RlJlM R`CaGeKjM RTDXF RRKWM RWRSZ R]M]VZ[ RdDiE R]Q\\Y RfG`GZH RdG^H RiGbG RkFeG',
	    'Po\\ZiZ R\\R\\[ R\\RiRi[ RbIjQ RfDkI R_DZI RbIZQ RTDXF RRKWM RVSS[',
	    'PoXOcO R^[a[ R[EaEa[ RiGdI RkDgH RkKgP RTDXF RRLWM RWSS[ RlTgYcZ R^H^M R[E[TYZ',
	    'Po\\NkN RZTlT RZGZJ RcJc[ RZGkGlJ RbCbF RTDXF RRKVM RWRS[ R\\K[Q',
	    'Po\\HlH RYQlQ R[XlX Rc[g[ RTDXF RRKWM R]CZK RWRS[ R]KjKiSh[ R\\KZX RcKbQaW',
	    'Po\\HiH R\\KiK RZNZR R\\DiDiK RZNkNkR RTDXF RRKWM RWRSZ R^SbWeYl[ R\\RhRdW`YY[',
	    'PoYHlH R]M`P RhNlR RTDXF RRKWM RbCbPeVhYl[ RhKeR RWRS[ R]KZR RbO`V\\YX[ RbLcT',
	    'PoaRlR R\\C\\[ RaIaT RgCg[ RaIlIlT R_G_L RTDXF RRLVM RWSSZ RYHXN',
	    'Po`VgV Rh[l[ R\\E\\[ R`P`X R`PgPgV R\\ElEl[ RfHiN RTDXF RRKWM RWRS[ RaH^N',
	    'Po\\RjR Rd[l[ R\\I\\R RdRdZ R[KjKjR RlXl[ R_FYL RTDYF RRKWM RVUT[ RXRUY R`C^H RaU^YY[ R_FfFcKbU',
	    'PoZFaF RZPlP R\\H]L RbGcK RTDXF RRKVM R^Uj[ RiGgL RWRSZ RaL]U RhPfUcX_ZZ[ RjD`E',
	    'Po[GjG RYLlL Ra[f[ RcCcL RfLf[ RTDWE R_W[[ RRLVM RVRSZ RiOkW R\\OZW R`L`S_X',
	    'Po\\JiJ R\\PiP R\\UiU R\\D\\U R\\DiDiU RhXk[ RfWjX RTDYF RRKWM RXRTY R^XX[',
	    'Po]IiI R]MiM R]RiR Re[l[ R]D]R RfRf[ R]DiDiR RlWl[ RTDYF RRKWM RXRUY R`R^W\\ZX[',
	    'PoYIbI R]C][ R`NaR RRKWM RjDdK RkLeR RTDXF RVSSZ R^IYT RlSgYb[',
	    'Po]LhL RZQkQ R^[b[ RcLc[ RgTkX RcCjK R]UYY RbCZK RTDXF RRKWM RWRS[',
	    'Po^KjK R\\ZlZ R\\E\\[ RdGdW R\\ElEl[ RdLiQ RUWT[ RbO^T RTDYF RRKWM RXRVX RdJaQ',
	    'Po[JjJ R[NjN RYPlP Rc[h[ R[F[M RbCbM R[FjFjM RTDYF RRKWM RXRUX R_P]UjUh[',
	    'Po[JjJ R[OjO R[TkT RY[l[ R[E[N RcOc[ R[EjEjN RTDXF RRKWM RWRS[',
	    'PocElE RZN`N RbOlO RZW`W RZEZY RZE`E`W RgEg[ RTDWE RRKVM RVSSZ',
	    'PoZElE R[SkS RX[l[ RcSc[ R[L^P R`LdP RfLjQ RiHfK R^H[K RcH`K RTDXF RRKWM RWRS[',
	    'Po\\PkP R\\UkU Rf[k[ R\\K\\[ RdCdK R\\KkKk[ R]D^I RlEhH RTDXF RRKWM RWRT[',
	    'PocGkG RYMlM R`UcU R]F]L RcCcU R^OZT RTDXF RRKWM RWRSZ RjPhTeWaYZ[',
	    'Po\\EjE ReNiN R_[g[ R`S`[ RgXg[ R]G\\K RaJ\\O RiSlY RTDXF R`PdR RRKWM RWRS[ R[TYZ RcF_M RjFiN',
	    'PoZOlO R[G[J R[GkGkJ ReTk[ RfJhM RbCcF R`U[[ RbNfW RTDWF RRLVM RWST[ RbP^X RbJ`U',
	    'Po[ElE R\\PlP R\\UlU Rh[l[ R\\K\\[ RdKd[ R\\KlKl[ RTDYF RRKWM R`GiK RXRTY RkEdH',
	    'Po[GgG RYLlL R\\PhP RZUlU R^[b[ RaCaK RbSb[ RTDXF RRKWM RVRSZ RiF`OXS RhPbR',
	    'PofMkM RbWlW Ra[l[ RbKbV RfEfV RSDWE R[WW[ RRKVM RVSSZ R]RZY RXE]EZM^M\\W RjD`F RZR\\W_Zf[',
	    'PoaMkM R`VlV R`ZlZ RfEfV RTDWG R[WW[ RRKVM RXE^EZM RWSSZ R]RZY RYM^M\\W RYQ[V^YfZ RjD`E',
	    'PoYLlL RSDWE RhEjH RhSaZ RRKVM RjOgT RVSSZ RbEYG RcRXT RlWk[i[ R^F^[Z[ ReDeNgY',
	    'PoZGkG R[QjQ R[K[Q RcCc[ R[KjKjQ RcQkY RaR[Y RSDXF RRKWM RWRS[',
	    'PoZIlI R[NiN R]ZiZ R^Q^[ R\\EiEiN R]SiSi[ R^RYW RTDXF RRKWM RWRSZ R`K\\T RaF]S',
	    'Po^JkJ R]RlR R]VlV Rg[l[ R]M][ R^D^J R^DkDkJ R]MlMl[ RRKWM RTDYF RWSTY',
	    'Po[HjH R^OgO R[D[H RjDjH RSDXF RbCbIgMlP RRKWM R[ThTe[ RWRS[ RcH]NYP',
	    'Po\\NkN RgXkX RcHc[ R[HkHkM R^C`G RiDfG RTDXF RRKWM RWRS[ RcQ]XYY R\\M[RlRkX',
	    'Po_IkI R]ZlZ R\\H\\[ ReCeI RTDXF RRKWM R^EXP RWSS[ RiLgX RaMbV',
	    'Po\\QiQ Re[k[ R\\I\\Q ReQeZ R[JiJiQ RkWk[ RgGlK R]HYL R`EgDiJ RTDXF RRKWM RWRS[ R^W\\ZX[ R`Q^X',
	    'Po]JjJ RZJgJ R\\OjO RYTlT RfEkK RSDXF RRKWM RbRdVgYl[ RWRS[ R`C]I R]K[Q RaU^YY[ RbOaV',
	    'PoZElE R\\NjN R\\TlT R\\IjIjN RTDXF RRKWM RWRS[ R\\I\\Q[X RdPdTbW`Z[[ RdTeWhZl[',
	    'Po\\QiQ Re[l[ R\\I\\Q RfQf[ R\\IiIiQ RlXk[ R_VZ[ R^C`G RTDYF RRKWM RXRUY RhCfI R`Q^X',
	    'Po[GlG R\\JkJ RZNlN RZRlR Rd[h[ RiNi[ R`UcX R^TYY RTDXF RRKWM RWRS[ RaM\\W RbI^T RbC`Q',
	    'PoZFlF R[JkJ Re[l[ RZNZQ RbCbJ RfQf[ RZNkNkQ RlXl[ R^W[[ RTDXF RRKWM RWRS[ R_Q^X',
	    'PoZFlF Rc[g[ R[L[O R_C_I RfCfI R[LkLkO R`V\\Z RTDXF RRLWM RWSTZ RbO`W R[RiRg[',
	    'PoZGkG RZPlP RbCb[ RbPjX RcPZX R\\I^M RTDXF RRLWN RhIfN RWSTZ',
	    'Po]FkF R_MjM R]SkS R_[l[ ReIeY RTDWG RSLUN RZE[H RXM[M[XX[ RVSSZ RcC_M R[X_Ze[',
	    'Po^IiI R^MiM R[ZlZ R[D[[ RcFcX R[DlDl[ RTDYF RRKWM RXRTZ R]QjQiVeV',
	    'Po]JjJ Rg[l[ R[O[[ R]D]J R]DjDjJ R[OlOl[ RdRiW RcR^W RTDYF RRKWM RWSS[ RdOaU RcKcS',
	    'Po]JiJ R\\M\\V R]D]J RcPcV R]DiDiJ R\\MjMjV RTDYF RRKWM ReWkY RXRTYS[ RaYZ[',
	    'PoZRlR R]I]R R[JiJiR RcJcRdUjZ R_FhFcK R_E[J RTDXF RRKWM RWRSZ R`C^H RbT^YX[',
	    'PoZRlR R_[c[ RcOc[ RhUlY R]VZY RaD[J RTDYF R_GcKjN RRKWM RWRS[ R_FiFdK_NZO',
	    'Po[FlF R^NiN R_[c[ R^I^N RdTd[ R^IjIjN RcCdF RTDYF RRKWM RXRUY R]QjQdT',
	    'Po^JhJ R^PhP R]XiX Rf[l[ RZIZ[ RcJcW R`ElEl[ RRKWM RZC^G RSDXF RWRSZ',
	    'Po_PfP R_VfV Rh[l[ RZIZ[ R_J_V R_JgJgV RaElEl[ R[D]G RSDWF RRKWM RWSS[',
	    'PoYK^K R_NlN RXQ^Q RX[\\[ RYKYQ RXE^E^K RbDb[ RhWlZ RSDVG RRLUN RiGdL RfQjY RjDhI RgYaZ RfNhW RUSS[ R]R\\[',
	    'PodNiN RcTjT RY[l[ R]R][ RcPc[ RTDWE RRLVM R]G[K RWSS[ RbI_M[N RcEaK R\\EkEiN',
	    'Po[FkF RZNlN R]ZiZ R]R][ RcCcG R]RjRj[ R^H_M RTDXF RRKWM RWRS[ RgHfM',
	    'Po]PiP R]ZjZ R]K][ RcCcG R]KiKiP R]TjTj[ RTDXF RRKWM RWRTZ R[G[J R[GlGlJ',
	    'Po[GjG RYKlK RZQlQ RZVkV RcCcK RcQc[ RhLfP R^M^P RTDYF RRKWM RXRT[',
	    'PoZEaE R\\QiQ RZKZO RZKkKlO RjFhJ R\\G]J RbGbJ RTDXF RRKWM R_ScXj[ RWRS[ RhRdW_ZZ[ RiC`E',
	    'PoZJdJ Ri[l[ R\\O\\X R`K`[ RZDdDdJ RhGhU RlDl[ RTDXF RRLWM RVSSZ RZDZRXY R\\OdOdXbX',
	    'Po[DlD R^JkJ R]OlO R]TkT R[[l[ RdFdO RdQdZ RTDXF RRKWM RWRS[ R[D[QYY',
	    'PoeVk[ R^VaY RhJlM RjIdN R`U[Z R`I[N RdRgX RTDXF R[HaK RRKWM R[T`V ReHiJ RdEkEhK RWRS[ R[EaE^K RZQaQ^W RcQkQiVfYb[',
	    'Po^EjE R^HiH R^LiL RZPlP R^E^[ RcSjZ RjTfU RcPeV RTDYF RRKWM RXRUY RcY\\Z',
	    'PoYGlG R\\L\\[ RbCbG RcOfQ RSDWF RRKVM RaNcThYl[ RVSS[ R]HYP RcHaN^Q RaLkLgTcY^[',
	    'Po\\RjR RZ[l[ R[G[J RcRc[ R[GlGlJ ReJkP RaJ[O RbCdG RTDXF RRKVM RWRS[',
	    'Poe[l[ R[F[J RcCcG ReLeZ R[FlFlJ R[P_U R_VZ[ RlWl[ RTDXF RRKWM RWRS[ R]KYS RaP]Y R\\MbM_W RdLjLjUgU',
	    'PoYDlD RZZkZ RZIZ[ RkIk[ R]L`O RiLfO RTDXF ReRiT RRKWM RWRS[ RaR\\T RaE_K RdIcW_W RdI_I',
	    'PoZJkJ RYOlO RZUlU R[[l[ R]J]U RZFcEc[ RiJiU RTDXF RRKWM RWRS[ RjDaE',
	    'PoYFlF R[NjN R[SjS R[J[S RbCb[ R[JjJjS RhXlZ RTDWG RbRiY RRKVM RWRS[ RbR\\XXZ',
	    'Po]KjK R_VhV R[ZlZ R[E[[ R_P_V RcGcO R_PhPhV R[ElEl[ RTDYF RRKWM RXRTY',
	    'PoZIlI RZRlR R\\EcEcO RdIgLlN RTDXF RRKWM RWRSZ RcH_LYN R_Q]VjZ RgRfVcYZ[ RiC^E',
	    'Po\\JkJ R\\OlO R^ZkZ R^T^[ RdJdS R^TkTk[ R\\EkEkJ RTDXF RRKWM RWRS[ R\\E\\PZX',
	    'PoZOjO R\\ZhZ R\\S\\[ RbKbS R\\ShSh[ RbDgI RbDZK RTDXF RRKVM R]KgJlK RWRSZ',
	    'PoYGlG R\\QiQ R^[b[ R\\K\\Q RbQb[ R\\KiKiQ RgUlY R]UYY RTDXF RRKWM RWRSZ RbCcG',
	    'Po\\IlI R\\VjV R\\ZjZ R\\E\\O R\\R\\[ R\\RjRj[ ReLiO RTDXF RRKWM RWRSZ RcEfM RlMkPiO RhC^E RdNZO',
	    'Po_OcO R\\UiU R\\ZiZ R\\Q\\[ RcCcO R\\QiQi[ ReHjN RjFgH R_JZN RcDfK RTDYF RRKWM RXRTY R[G`G]L',
	    'PoZElE RhSlW R[O_R`W RkJiMfMjV RSDXF RRKWM RWRS[ RcE_JYL R_JbM^PYR RdP^VYX RdGhT RdFgQ RaKdPeTdZ RdOeW Rc[^[',
	    'Po[LjL RcRjR R[G[J RcLcZ R[GkGkJ RTDXF RRKWM RWRS[ RcCcG R^PZZ R\\T^WaZh[m[',
	    'Po[GkG RZMkM RYQlQ R^I_K RaDcF R_Q\\U RTEXF RRKVM RWRSZ R`N_R RgHeM R\\ViY RhQfUcXZ[',
	    'Po\\HiH R\\LiL RZOkO RYSlS Rb[g[ R\\D\\L RgOg[ R\\DiDiL R^UbX RTDXF RRKWM RWRSZ',
	    'Po\\IiI RZMlM R^[i[ R^R^[ RfMlS RgDkH RiXi[ R]D[H R_MYT RdIgO RTDXF RRKWM RWRS[ RaH]P RbC_N R^RgRfWcW',
	    'Po\\UkU R\\ZkZ R\\O\\[ RdOdZ R\\OkOl[ R[I^L RaIeM RgIlN RkCgH R^EZH ReDaH RTDYF RRKWM RXRUX',
	    'PoYJaJ RcLlL R]C][ RiLi[ R]MaQ RTDXF RRKWM RVSSZ R]JXV R]G[P RkDcFcRbY',
	    'PoZLlL R^SjS R^WjW Re[i[ R_O_[ R]QjPj[ R_NYT RTDXF RRKWM RWRS[ RaI^P R\\DjI RjDcGYJ',
	    'PoZGkG R^KhK R^PhP RYTlT R^C^T RhChT RgWk[ RTDXF RRKWM RWRSZ R^V\\YX[',
	    'Po[JlJ R_RlR R^ZlZ R^U^[ R_M_R ReKeZ RlMlR R[ElElJ RlUl[ RTDYF RRKWM RWRTZ R[E[QYY',
	    'Po[IjI R[NjN RZQlQ R[D[N RbDb[ R[DjDjN RTDXF RRKWM RbQiXlY RWRS[ RbQ\\XYY',
	    'PoeMjM R]UcU RfUlU R]YlY R]P][ RfPlPl[ R_IZM RTDYF RRKWM RWRTY RaC]L R`GlGjM RcO]P',
	    'PoXJaJ RbJlJ R]C][ RfCf[ RTDWG RRLUN R\\KaO RUSS[ R]JWW RgJaV RgJlV',
	    'Po_VfV Re[j[ R[L[[ R_P_X RcCcL R_PfPfV R[LjLj[ RgElJ R^FZJ RTDXF RRKWM RWRS[',
	    'Po_IiI R^MiM R_VhV Rg[l[ R_Q_X RdFdM R_QhQhV R[ElEl[ RTDYF RRKWM RXRTZ R[E[RZY',
	    'PoZFkF R[JjJ RXNlN R]RgR R[VjV RbOb[ ReNkT R_NXT RdJfP RTDXF RRKVM RVSSZ RaH]Q RbD^N',
	    'Po[HjH R[MjM R[PkP RYTlT R[D[M R[DjDjM RTDXF RRKWM RbScVfYl[ RWRS[ RaV_YZ[ RcPaW',
	    'Po]IkI R]MkM R[UlU R[ZlZ R[Q[[ R]E]M R]EkEkM R[QlQl[ RTDYF RRKWM RWRTZ',
	    'Po]GaG RXMbM RZ[][ R]C][ RTDWE RRKVM RaPbU RVRRZ RYPXW RcFlFjOgUa[ RdGgSiXm[',
	    'PoZFlF RYMlM R[PiP RYSlS RbCbP R[IiIiP RTDXF RRKWM RWRS[ R`P^T\\WjZ RgTdXaZY[',
	    'PoZFlF RZMlM Rc[h[ R_C_J RfCfJ RRLVM R]WY[ RcJcL RTDWE RVSSZ R_R\\Y R`M]X R_RiRh[',
	    'PoZGkG R\\NiN R\\RiR R\\WiW RYZlZ R\\J\\Z R\\JiJiZ RTDXF RRLWM RWRT[ RbCaK',
	    'Po[GjG RYKlK R[UjU RX[l[ RbCbK RbQb[ RfKfQlPkQ R_N[R RTDXF RRKWM RWRS[ R_K]P',
	    'PobFlF R\\NjN R\\RjR RYVlV R\\J\\R RbCbJ RbRb[ R\\JjJjR RTDYF RRKWM RWRTY',
	    'PoZQhQ Re[j[ RaJa[ RcSgW R`QdU RaQZX R^GYK R\\L]O RTDXF RRKWM RWRS[ RfKeO R_C\\IlHlSj[',
	    'Po]MgM RZQiQ R\\WgW Re[j[ R\\S\\Y RaLaW RgSgX RTDXF RRKWM RWRS[ R^I\\O R\\HlHlUk[ R^D\\HXL',
	    'Po\\LjL RZQlQ R_[c[ RZGZJ RcCcG RcQc[ RZGkGlJ RhUlY R]UYY RTDXF RRKWM RWRS[',
	    'Po\\NjN R[TlT R\\IjIjN RgOjQ RTDXF RRKWM RWRS[ R\\E\\QZX RiD\\F RdPdTbW_Z\\[ RdTeWhZl[',
	    'PoZHdH RYMeM Ri[l[ RZQZY R_C_[ RgFgT RlCl[ RTDWF RRKWM RWRSZ R[DYK RZQdQdYaY',
	    'Po[KbK RfKlK R[RbR ReRlR R^[a[ Rh[l[ R[EbEb[ RfElEl[ RTDXF RRKVM RWRSZ R[E[QZY RfEeTd[',
	    'PoYK^K RbQkQ RYR^R Rb[k[ R[[^[ RYD^D^[ RbEbZ RfEfP RbEkEkR RUDWF RlWl[ RRKVM RVSSZ RYDYSXZ',
	    'PoYJ`J R`ZkZ R]C][ R]N`Q RhTk[ RTDWF RRKWM RVSSZ R\\MXU RdEhDkM R\\HYT RcFaN ReMcZ',
	    'Po[NjN RXUlU R[H[T R`C`T ReCeT R[HjHjT RhWk[ ReWhX R\\YX[ R^V[Z RTDXF RRKWM RWRSZ',
	    'Po[IjI R[NjN RYRlR R[D[N RcDcN RgRg[ R[DjDjN R^WZ[ RTDXF RRKWM RWSS[ R_R]Y',
	    'PoiFfI RjQfU RbJkM RTDXF RRKWM RbVdXgYk[ R]E[J RWRSZ R]Q[V RcDaL[O RbOaW^YZ[',
	    'PoYHlH R[RaR R[L[R R[LaLaR RhCjF RgT`[ RTDXF RRKWM RjLfV RWRSZ RcUYX RlWk[i[ ReCeNgY',
	    'Po[KaK RdKjK R[QjQ Re[l[ R\\F\\Q ReQeZ RdFjFjQ RlWl[ R^VZ[ RTDXF RRKWM RXRUY R_Q^X RaD[E',
	    'PoXHbH R\\[_[ R[N`NaZ RhXk[ ReNiR RgCkL RTDWF RRKWM RcViX RVSSZ RgCbM R]C\\H\\OZX',
	    'Po\\JgJ RYNlN R^[b[ RbNb[ R[P^S RiPfS RTDXF RRKWM RbNeThXlY RWRS[ R`UYY R^C\\K R^FgFfN',
	    'Po]LhL RZQlQ RZ[l[ RcLc[ RcDkL RbEZM R\\T_Y RTDXF RRKWM RiSfY RWRS[',
	    'Po`[h[ R[G[K RaNaZ R[GkGkK RbCcG R_JdK RTDXF RRLWN RWSS[ RgKdS^YZ[ RjOlV RiVh[ R[OZW',
	    'PoYPlP R\\UiU R^[b[ RbLb[ R\\LiLiV RhFkI RaGcJ RTDXF RRKWM R\\EYK RWRSZ RiC\\E',
	    'Po\\FjF RZJlJ RcCcJ RfJfOkOkP RaPZV RTDXF RRKWM RWRS[ R_J^OZP R^SbWfZl[ R^ShSdW`YY[',
	    'Po]KgK R[UjU R[O[[ R`O`[ ReOe[ RbCjK RbCZL RTDXF RRKWM RWRS[ R[OjOj[g[',
	    'PoYTlT RZ[k[ RbGbL RTDXF RRKWM R]EZL RWSS[ RiD]E RiM]O RhGlL RcNc[',
	    'PoZGkG RYTlT RcCcG RcQc[ R^LaO RgLlQ RgLbP RTDXF RRKWM RWRS[ R_I[P RgIeN',
	    'Po[KjK R]QkQ RdNd[ R[FjFjK ReRlY RdQgU RcCdF RbS\\Y RTDXF RRKWM RWSSZ RbQaU R[F[QZX',
	    'Po\\JkJ R]NjN R]TjT R]ZlZ R]H][ RdGdZ RTDXF RRKWM RWRTZ R_CZN ReDdJ',
	    'PoZFlF RZLjL R\\SiS R\\WiW Rd[i[ R\\O\\[ RbCbG R\\OiOi[ RSDWE RgHkM RRKWM RWRS[ R`F]L',
	    'Po^JgJ R^[f[ R_T_[ RfXf[ RaSdV R_RbT RhUkY RTDXF RRKWM RbCgHlJ R[MhMeS RWRS[ RZUXZ RaC\\IXJ',
	    'PofLlL RZQlQ RcNc[ RfEfL RdRjY RTDXF RRLWM RWRS[ R_KZM RaE_K R[EZI R[EkEkH RcQ\\XXY',
	    'PoaFjF R]NcN RaQjQ Re[l[ RbKbQ RaCaI RfSf[ R[JlJkM RlXl[ RTDYF RRKWM RWRT[ R^Y[[ R`S_Y R[I[SYY RgMaM',
	    'PoZFlF R\\MiM RYUlU R^[c[ R\\I\\M RcCcF RcSc[ R\\IiIiM RTDXF RRKWM RWRS[ R[QiQcS',
	    'PoeQiQ R^[f[ R_U_[ RgXf[ RhUlZ RdL`P R]FYJ R`JZO RSDXF RRKWM R`SdU RWRSZ RaF^M R[UYZ RfFbO R^C\\HjGiQ',
	    'Po[KaK RfKlK R[OlO RfRlR RaDaK RfDfK RfRf[ RlDl[ RTDXF RRKWM RWRSZ R[D[OYZ R[SaSa[',
	    'PoZGlG RbCb[ R\\M_P RbPjX RgMkP RbPZX RSDXF RRKWM RgJeP RWRS[ R]JZQ',
	    'Po[IjI R[MjM R[TbT Rd[l[ R[D[M R[P[Z RePe[ R[DjDjM RlXk[ RTDXF RRKWM RWRS[ RkRdU RbYX[',
	    'Po[EkE R[IjI RYLlL R\\UjU Re[i[ R\\P\\[ RcCcL RcPcU R\\PjPj[ RTDXF RRKWM RWRT[',
	    'PoYGkG R[QiQ R[ViV Rb[k[ R\\L\\W RbKb[ RZNiMiV ReHkN RkXk[ R^IXO RSDXF RRKVM RWRS[ R`C]K',
	    'Po`GjG R]GgG RZHcH R`SkS R]ShS R[SfS RTDXF R_CaIdLiN RdPiP RRKWM R`PbWdYi[ RWRS[ RgJaLZN RlLkNhN RgVaYY[ RlXk[h[ RdDiD',
	    'PoXJkJ R\\[`[ R`O`[ RiSkX RdJkQ RSDXF RRKWM RVSSZ R\\RZW RdSeX RiD]E RbE^LYQ',
	    'Po[N_N RV[X[ Ra[e[ RYOY[ R_C_N ReOe[ RfUlZ RYS]W R\\RZT RaV]Z RjQgU RfGbI RgEeG RePhW R_CaHeLjN RSRWRTY R^RcR`X RUG\\GXLSN',
	    'PoZGlG RcQjQ RYZlZ R]N]Z R^C^J RcKcZ RgCgJ RTDXF RRLWN RWSS[',
	    'PoYHeH RVKhK RSOkO RZ[_[ R_H_O R_Q_[ RZL[O RdLcO RSS[TWXRZ RhRaU R_SbVfXlZ R`ElH R_CZFRH',
	    'Po]JkJ R]NkN RiYlY R\\R\\Y R]F]N RcCbF RdNd[ R]FkFkN R\\RlRlY RTDXF RRKWM RXRUY',
	    'PoZGkG RYTlT RbIbQ RgPg[ RdGjL RaGZM RTDXF RRKWM R]MhLlM RWRSZ RaC^J R[ZY[ R^Q]X',
	    'Po_NbN RZS_S R_[b[ RbOb[ RbCbN RTDWG RRLVM ReIjM RiEeI R^TYY R_IZN RiQeU RbSfWlZ RbHgJ RVRSZ R[G_G]L',
	    'Po\\HiH R\\LiL RZQkQ R\\D\\L R\\DiDiL RbNcP RTDXF RRKWM RWRTZ R]SbWfYl[ RhRdW_YY[',
	    'PoZGbG RdGlG RZNlN R^ZjZ R_R_[ R_C_J RhChJ R]SjSj[ R_QZV RTDXF RRKVM RWRT[ RbJ^S',
	    'PoYH_H ReHlH RZN_N ReNlN ReTlT ReCe[ R^WZ[ RTDXF RRKWM RWRSZ R_D_TXT R_O^W',
	    'Po[EkE R[IjI RZLlL R]SjS R]VjV Rf[j[ R]O][ RcCcL R]OjOj[ RTDXF RRKWM RWRS[',
	    'PoYPeP Rg[l[ RYIcIb[ RgEgT RlDl[ RUDXG RRKVM R\\D\\G RWRSZ RbD`I R\\I\\RZZ',
	    'Po]HjH R]LjL Re[j[ R]D]L R]Q]X R]YeYfW R]DjDjL R`NYT RTDXF RRKWM RWRS[ ReS]U R]QlPj[',
	    'PoZFlF R\\ViV RcCcG RTDXF RRKWM RWRSZ R]FaJeLlN RhFdK`MZN R]O]T[Z RiOi[ R]QiQ',
	    'PocGkG RYLlL R_UcU R]F]L RcCcT RhOlS R^OZS RTDXF RRKWM RWRS[ RiSeXaY[[',
	    'Po]NjN RcEcZ RlDl[ RgRiV RcNgR R`S\\W R_G`L RSDXF RRKWM RWSTZ RcM`T RhFgL R[D[RZY',
	    'PocHkH RYMlM R]F]M RcCcM R\\O_S RjOeS RTDXF RRKWM RWRS[ R`WYZ ReWk[',
	    'Po[IhI RYMlM R^[a[ RbMb[ R[EhEhM R[P^S RTDXF RRKWM RiPgRbSfWlY RWRT[ RaTXX',
	    'PoZFkF R[IjI RYLlL R\\O\\V RcCcL R\\OjOjV RTDXF RRKVM RdXk[ RWRSZ RaX[[ RcRbX',
	    'Po\\FkF RZTlT RcCcJ R^MbO RTDXF R[P_Q RRKWM ReWjY R[JkJjN RWRSZ RdLdT_XZ[',
	    'Po[GkG RZOlO R]XdX Rd[i[ R]R]Y R]RdRdW RiOi[ RTDYF RRKWM RbIkM RXRTY RcH`K[M RcCbJ',
	    'PoYGbG RdLlL RYPaP R^K^[ RiLi[ RTDXF RRKWM RVSSZ R]DYO RbTXU RkDdFdRbY',
	    'Po[JiJ RZQjQ RZVjV Rb[k[ R[M[W R[E[I RcJc[ R[EiEiI RZMjMjV RlXk[ RTDXF RRKWM RWRS[',
	    'Po[KkK R[PkP RYUlU R[F[P ReQe[ R[FkFkP RbO\\T RaD_F RTDXF RRKVM RWRSZ RcK`R RcGbP',
	    'PoZOkO RYTlT RbLbT R[H]L RaGbK RTDXF RRKWM RcTgYl[ RiGgL RWRS[ RbT^YY[ RaE[E RjC_E',
	    'Po[PjP R[VjV RXZlZ R[J[V RcJcV RZKjKjV R_C]GYL RTDXF RRKWM RWRS[ R^FfFcJ',
	    'PoZIdI RYPdP Rh[l[ RgGgV RlCl[ R_I^ScX R]VY[ R[C]H RTDXF RRKWM RVSSZ RbCaH R_N]W',
	    'Po[PkP R[UkU RZGZJ R[K[W RcIc[ R[KkKkV RZGlGlJ RaCcE RTDWF RRLWN RWSSZ',
	    'PoYIjI RYMlM RgFkJ R`E\\I RTDXF ReMhPlR RRKWM RVSSZ RaK\\PYR RbQ[S RfS\\W RhVcY[[',
	    'Po`PdP RZSkS RbQb[ RdIdO R[JkJhM RTDYF RRKWM RbSgWlY R`GfJ RWRS[ R\\EiEbH RcJ`M[O RbS^WYY',
	    'PodOiO RXTlT R\\I\\T RdIdN RZJiJiT R`M]P R^FfFbJ R^FYK RTDXF RRKWM RbScVfYk[ RWRSZ RbR`W^YX[ R`J_O',
	    'Po[GgG RZLlL R^UjU R^ZjZ R_P_[ RaCaL R]QjQj[ RTDXF RRKVM RWRSZ RkEbNYR',
	    'PoZHkH R\\LcL R]UcU R]O]W R]OcOcU RhCjF RSDXF RRKVM RVRSZ RZHZRXY ReDeLhZk[lW RjMgUa[',
	    'PoZEkE RgQjQ RZTlT R[I[Q RaHaQ ReIeP R[IjIjQ RTDXF RRKVM RaTeYk[ RWRSZ RbSaW_YZ[ RaE`L',
	    'Po][`[ RZOZ[ RZOaOa[ RSDWE R\\R_S R\\U^V R[EbEhJ RcTf[ RgTj[ RRKVM R]KfJlK ReNcU RjNgU RVSSZ RaD[JXL',
	    'PoZHlH R\\PiP R\\UiU R\\ZiZ R\\L\\[ R\\LjLj[ RgCfG R^D_H RTDXF RRKWM RWRTZ RbHaL',
	    'PoZFlF R]MiM R\\SiS R^[c[ RZPZS R]I]M RcCcF RcSc[ R]IiIiM RZPlPlS RTDXF RRKWM RWRS[',
	    'Po\\DkD R\\LjL R\\PlP RRSlS R\\D\\O R_P_[ RXKSP RTDYE RRGWI R`ScWlZ R\\HiHiL R]SZWRZ',
	    'Po[GlG R[KlK R`OhO R`H`O RcCdG RhHhO RTDXF RRKWM RWRS[ R_TcXgZl[ R]SjSeXaY\\[ R[F[QYY',
	    'Po]SeS R]L]S R]LeLeS RlWl[ RSDXF RRKWM RVRSZ ReG\\H RZEiEiUj[ RZEZQXY RaHaX[Y ReUfY_Y',
	    'PoZFlF R\\SiS R\\WiW RX[l[ R\\O\\[ RbCbN R\\OiOi[ RbF[M RbFhLlM RTDXF RRKWM RWRS[',
	    'PoXGcG Rg[k[ RZ[][ RXKXM R]C]K R]R][ RXKcKcM RdJlJlZ RTDVF RYOaO]S RRKVM RVSRZ RgCgNeW RcSXU',
	    'Po[IkI R[LlL R]RkR R]VjV R[ZlZ RdSdZ R[DkDkI RhOkR RTDXF RRKWM RWRS[ RaM_R R[D[QYY',
	    'PocHiH R_WgW Rg[l[ R[N[[ R^D^N R_Q_X RcHcN R_QgQgW R^DiDiN R[NlNl[ RTDYF RRKWM RXRTZ',
	    'Po[FkF RfYjY RZLZP R\\Q\\Y RcCcG RcNc[ R\\QjQjY RZLlLlP R^H_K RTDXF RRKWM RWRTZ RhHfK',
	    'Po\\IjI R\\NjN RYQlQ R\\D\\M R]Q]Z RcDcM R\\DjDjM RTDXF RRKWM RcQeVhYl[ RWRSZ RkSeV RdYYZ',
	    'Po\\IiI R\\NiN RX[l[ R[R[[ R\\E\\N R`R`[ ReRe[ R\\EiEiN R[RjRj[ RTDYF RRLWN RXRTZ',
	    'Po\\NkN RcViV R]WdW Rh[l[ R[P[[ R\\H\\M R[EcEcV R\\HkHkM RYQlQl[ RhTiX RTDYF RRKWM RXRUY RkDcE',
	    'PoYGlG RaLhL R\\OkO RYSlS R\\D\\O RaDaL RbOb[ RhDhL RTDWG RSKVN RbRhXlZ RVRSZ RbS]XXZ',
	    'PoZJbJ RZObO RZTbT Rg[l[ RZEZT RZEbEbT RfFfU RlCl[ R`VdZ R\\VX[ RTDXF RRKWM RWRSZ',
	    'Po[HlH R[LlL R]SkS R]VkV Rg[k[ R[D[L R]O][ RdDdL R]OkOk[ R[DlDlL RUWT[ RTDYF RRKXM RXRVX',
	    'PoZGlG RZLlL R\\VdV Rc[i[ R\\P\\W R_C_J R\\PdPdV RfCfJ RiLi[ RTDXF RRKWM RWRS[',
	    'PoZGkG RXLlL R\\UfU R][i[ R]U][ R]PfPfU RgCfMlS RiXi[ RTDXF RRKWM R^C^MXSS[',
	    'Po\\IiI R\\QiQ RZVlV R\\L\\Q RgSg[ R\\LiLiQ RUVT[ RTDXF RRKWM RcDeFlI RWSUY R]ZZ[ RbC^GZI R_S^Y',
	    'PocFlF RYQaQ Rc[l[ RYIYS R]D]Y RYIaIaQ RgFg[ RTDWG RRLVM RWSS[ R`TbYXZ',
	    'Po]JhJ R]SiS R]WiW RY[l[ RZGZJ R]N]W RcCcG R]NiNiW RZGkGkJ RTDXF RRKWM RWRS[',
	    'PocJiJ RZK^K RcPiP RY[][ RYE^E^K RcEcV R`EkEi[ RTDWF RRLUM RVSSZ RYKYR^R][ RkUgUbV',
	    'Po]IkI R]MkM R]E]M R]EkEkM R_NYT RTDXF RRKWM RWRS[ R]QlQk\\g[ RdR`W RcUgW R[S[ZdZ',
	    'PoZHlH R[SjS R[ZjZ R[M[[ R_C_K RcMcZ RgCgK R[MjMj[ RTDWE RRKWM RWRS[',
	    'PoZFkF RYSlS R[L[S R^C^I RfCfI R[LiLiS RSDXF RRKWM RdUgYl[ RWSS[ RaU^YY[ RbIbQaV',
	    'PoZFlF R]RiR Re[k[ R]L]R RcCcF RfRfZ R]LiLiR RkXk[ RgHlL R`V\\Z R^HYM RTDXF RRKWM RWRSZ R`S^Y',
	    'PodHlH RXHbH RbRlR R[[_[ R[N`N`Z RSDWF RRKWM RVSSZ RZTW[ ReDbK RdLkLgPg[c[ R]C\\H[OZX',
	    'Po]JeJ RZMhM R\\OfO R\\RfR R\\XfX Re[j[ R\\U\\Y R\\UfUfX RTDXF RRKWM R^C[J RWRS[ R\\HlHlUk[',
	    'PoYK_K RYQ_Q RYW_W RYEYY RYE_E_W RSDWE RRKVM RVSSZ RjPiUeX_[ RjHlO RcHaP RfCfScS',
	    'Po]RjR R]VjV R]ZjZ R^O^[ RcCcK R[OjOj[ RgFlJ R^EZJ RTDXF RRKWM RWRS[ RfJ\\O R`KcLYO',
	    'PoZGkG R[KjK RYPlP RYTlT R^C_G RgCfF RTDXF RRKWM RcGbSdVfYk[ RWRS[ RaU^YY[ RbNaV',
	    'PoZGcG RXLlL RZWbW RZOZX R^C^L RZObObV RTDWE RgEkH RhSa[ RRKVM RjOgV RVSSZ RlWk[i[ ReCeMgY',
	    'PoZIkI R\\PjP R\\TjT RYWlW RZDZH R\\L\\T RbFb[ R\\LjLjT RZDkDkH RTDXF RRKWM RWRS[',
	    'Po[GkG R[OiO RYSkS R^[b[ RcCcM RcSc[ RgVkZ R]VYZ RTDXF RRKVM RdGgJlL RWRS[ RaG^KZL',
	    'Po^NhN R[SkS RbSj[ RfIkN R\\H_JZN RhEeG RdDhL RTDYF RRKWM RXRUY R[EaE^K RkHgJ RbU^YZ[ RcNaV',
	    'PoXI_I RbMjM RbYjY RX[\\[ R\\C\\[ RbQb[ RbQjQj[ RSDVG RhGlL RfChI RdH`L RRLVM RUSRZ ReCcJ R_PXQ',
	    'Po[JaJ RdJkJ R\\MjM RYQlQ Rb[g[ R[D[J R[DaDaJ RdDdJ RdDkDkJ RTDXF RRKWM RWRS[ R]UiUg[ R^Q]V',
	    'PoaElE RbJkJ RYLaL RbPkP RaUlU RgEg[ RSDWE R\\WX[ RRKVM RVSSZ RaRXS RaDYF R]E]S\\X',
	    'Po\\JlJ R_RlR R_VlV R_ZlZ R_N_[ RdEdJ R_NlNl[ R\\ElElJ RTDYF RRKWL RXRS[ R\\E\\QZY',
	    'PoYGkG R[RjR R[K[R RbCb[ R[KjKjR RTDWG RbQhXlY RfLeP R]M_P RRKVM RWRS[ RbQ\\XXY',
	    'PoZGkG RYMlM R\\UiU R\\ZiZ R\\Q\\[ R\\QiQi[ R^H^L RbCcG RTDXF RRKWM RWRS[ RgHeM',
	    'Po[FkF RZLlL R\\SiS R\\ViV Re[i[ R\\O\\[ R\\OiOi[ RcCcF R^G_K RTDXF RRKVM RWRSZ RhGfL',
	    'PoZGkG R^KgK R^OgO RYSlS R^C^O RcOc[ RhChO RTDXF RRKWM RcRhXlZ RWRS[ RbR]XYZ',
	    'Po_LlL RaZjZ R\\C\\[ RaRa[ RfEfR RaRjRj[ R_G_K RTDXF RRKWM RVSSZ RZHYN RjD`F',
	    'PoZFkF RZIjI RYMlM R]QgQ R[UiU RSDXF RRKWM RbUeYj[ RVSSZ RbD`LYT RcIeNkR RbQaV^ZZ[',
	    'PoZIlI R\\QjQ RZSkS R[XlX R\\U[Y R\\L\\P ReQe[ R\\EiEiI R\\LjLjP RTDXF RRKWM RWRS[ RaC`J',
	    'Po\\OhO R\\ShS R\\L\\Z R\\LhLhS RcCgH RTDXF RRKWM RbTfYl[ R_HfHlJ RWRS[ RjUeW RbC]HYJ RdYYZ',
	    'Po\\JjJ RZMlM Rh[j[ R[R[[ R\\E\\J R`R`[ RcCcJ ReRe[ RjEjJ R[RjRj[ RTDXF RRKVM RWRSZ RcMaQ',
	    'PoZElE R`OfO R`TfT RZYkY RZJZ[ R`J`Y RfJfY RZJkJk[ RTDXF RRKWM RWRS[ RbEaI',
	    'PoZEkE R\\MiM R\\RiR R\\ViV R\\I\\V R\\IiIiV RSDXF RRKWM ReXl[ RWRS[ RcEaI R`XY[',
	    'PoYJbJ RdQlQ RdYlY R^C^[ RdHd[ RgCgI RdHlHl[ R_NaP RTDWE RRKWM RWSS[ R]HZT',
	    'PocIiI RbMlM R]SiS R]WiW Re[i[ R]P][ RcEcM R]PiPi[ RTDXF RRKWM RZEkEjH RWRS[ R^G[N R`LgM',
	    'Po\\IiI RZLlL R]PiP R]TiT RaXlX R[XiX RYYeY R\\D\\I R]L]X RiLi[ R\\DiDiI RTDYF RRKWM RXRUY',
	    'PocQgQ RX[l[ R[S[[ R`S`[ ReSe[ R[SiSi[ R`EfEkJ RaL\\Q R^FYK RTDXF RRKWM RWSS[ R\\JhJgP',
	    'PoYHlH RZLZ[ ReLeV R\\O_Q R\\T_U R]C_G RTDXF RRKWM RWSSZ RgCfG RZLaLa[^[ RjKj[g[',
	    'Po_IlI RaMjM R`QkQ R_UlU Rb[l[ ReCeX RaFjFjM RTDWG RRLVM R[WW[ RWSSZ R]QZY RYE^EYM^M[W RYRZV]XaZh[',
	    'PoXIcI ReKlK ReRlR RZXaX Rh[l[ RZPZZ R^D^P RZPaPaW ReDlDl[ RTDXF RRKWM RWRSZ ReDeRdX',
	    'PoYGbG RcJlJ RXMcM RYRbR Rf[j[ R^C^M R^O^Y RjCj[ ReNfT RTDWF RRKWM RVSSZ RcWXY',
	    'PoYJbJ RdKlK RdRlR RdYlY R^C^[ RdDd[ RdDlDl[ R^MbQ RTDXF RRKWM RVSSZ R^JXV',
	    'PoZFkF RbOeO RYTlT RfFfO RhIkL R\\IZM R`L\\P RbCcF RTDXF RRKVM RcUgYl[ RWRSZ RcScV RcQaV^YY[ RaF`M',
	    'Po\\MeM R\\SeS R\\VeV Rh[l[ R\\P\\[ RiDi[ RTDWG RcJfM RaD\\I RcF\\M RRKUM RlWl[ RVRSZ R\\PePe[a[ RZDZRXY RbI\\I',
	    'PoZFlF R]JhJ R]MhM RXQlQ R[ZlZ R[Q[[ R]C]Q RhChQ RTDXF RRKWM RVSS[ RaQ^W ReQeVjVjU',
	    'Po\\HiH R\\LiL RZOlO RcTjT Rb[l[ R\\D\\L RcOcZ R\\DiDiL RTDXF RRKWM RWRS[ R]SZZ R\\T^W`Zg[',
	    'Po\\GbG RdNlN R\\UjU R\\ZjZ R\\D\\N R\\Q\\[ ReDeM R\\QjQj[ RlKlN RbNaQ RTDXF RRKWM RWRSZ RkFdH RbMYN',
	    'PocFlF R\\NjN R\\RjR R\\VjV R\\J\\V RcCcJ R\\JjJjV RTDXF RRKWM RfXl[ RWRTZ R`XY[',
	    'Po\\JjJ R\\NjN R[RkR R\\VjV RZ[l[ R\\F\\N RcRc[ R\\FjFjN RTDXF RRLWM RWRS[ RbCbF',
	    'Po[IjI RZLlL R[D[I RcCcI RjDjI RfVjZ RdRgX RTDXF RRKWM RjPhU RWRS[ R\\SY[ RcV`Z][ R`P`U ReNcW R^J\\W',
	    'Po\\IkI R\\NkN R`ViV R]WfW Rg[l[ R[P[[ R\\E\\M RcEcV R\\EkEkM RYQlQl[ RgSiX RTDXF RRKWM RWRS[',
	    'Po[ElE R]MjM R[UkU R[ZkZ R[P[[ R]H]M RcPcZ R]HjHjM R[PkPk[ RTDXF RRKWM RWRTZ',
	    'Po\\HjH R\\KlK R\\ViV R\\ZiZ R\\D\\P R\\R\\[ R\\RjRj[ RTDXF RRKWM RcHeMgOkOlQ RWRS[ R\\DjDjH RdNYP',
	    'PobHlH RXH`H R[[^[ R[N_N_Y RgJgW RdLc[lYl[ RSDVF R[C]F RRKVM RVSRZ RjMbQ RcCaK RkMkUhU R[H[PYY',
	    'PoZRlR R[F[J R[FkFkI RTDWF RfNhP RaCdF RRKWM RbQdUhYlZ RfJkL RWRS[ R`J^LZN RaU]YY[ RcN`W',
	    'Po\\IjI R\\MjM R\\RkR R\\VjV RZ[l[ R\\D\\M RcMc[ R\\DjDjM RTDXF RRKWM RWRS[ R]O[T',
	    'Po\\MkM R\\QkQ Re[j[ R\\J\\Q R\\JkJkQ RTDYF RRKWM RXRTZ R[EkEdH R`GgI RZTlTk[ RdJdQbV`Y[[',
	    'Po]HiH R]LiL R]PiP RZTkT R]E]P R]EiEiP RTDXF RfQiR RRKVM RbSdWfYk[ RWRSZ RaV_Y[[ RbRaX',
	    'PoZIlI R[PjP R[TjT R[WkW RY[l[ R[L[S R[E`EdEc[ R[LjLjS RTDXF RRKWM RWRS[ RjDaE',
	    'Po]JfJ RZMlM RhVlZ RfRiX RTDXF RRKVM RWRSZ RaNZQ RaRZU RjOcS`WZZ R^C]J R^FgFfM RdVd[`[ R`OcRdX',
	    'PoYKbK R^D^[ R]MaP RhTk[ RTDWE RRKVM RfRb[ RVRSZ R^LYW RkIjP RgCgOdY RgLiX R^I\\Q RcIcO RaDZE RgKeV',
	    'PoRH]H RZ[_[ RYDYP R_P_[ RXH[K RkFhI RYHRN RdGfKhNlO R_RdWkZ R`F^J RdGcKaM]O RST[TWXRZ RiSbV RdDeK R\\DSE',
	    'Po[HlH RfRkR R\\VkV R\\ZkZ R\\M\\[ RaHaP RfHfQ R\\MkMk[ R_C`F RhDeI RTDXF RRKWM RWRSZ R_R]S',
	    'PoZElE R[PkP R[UkU RY[l[ R[I[O R`E`O RcQc[ ReEeO R[IkIkO RTDXF RRKVM RWRS[',
	    'Po\\HiH R\\LiL RYOlO Re[i[ R\\D\\L R\\DiDiL R^PYU R`TZZ RTDXF RRKWM RWRS[ R\\SjSi[ RfSdW`[',
	    'Po^OhO R]TjT R]WiW RZ[l[ RcQc[ RaD[J R_GcKkO RTDXF RRKWM RWSS[ R]Q[V R_FiFdK[O',
	    'Po[FkF R[IjI R[MkM RYPlP R]ZkZ R^T^[ RcCcO R\\UkUk[ RTDXF RRLVM RVSSZ RaP\\UXW',
	    'Po\\KjK RZOlO RhFlJ R]WY[ RbFcI RTDXF RRKWM R_RaTeXl[ R\\EZK RWRSZ R_P[Y R_SjSeX^[ RaK]W RiC\\E',
	    'PoZFlF R^QhQ R]UiU Rh[k[ R[K[[ RcCcK RcQcY R[KkKk[ R_M`O RTDXF RRKWM RfLeO RWRS[',
	    'PoYIkI RXZlZ R_I_Z ReHeZ RTDWG R\\D^H RRLVM RWSS[ RgCdI RZN\\U RjMhV',
	    'Po[GlG R\\JjJ RYNlN RbGbN R^C_E R`OZU RgCfF RTDXF RRKWM RdUhYl[ RWRS[ R^RfRaXZ[',
	    'Po\\JiJ R\\NiN RYR_R R^[b[ R\\F\\N RbNb[ R\\FjFjN RdTjY R^TYZ RkQgU RbOeV RTDXF RRKWM RWRS[ RbCaF',
	    'Po[FjF R[JjJ RYNkN R^VgV R^ZgZ R^P^[ R]QhQh[ RgOlT R^OYT RTDXF RRKVM RWRSZ RaH]Q RbC_O',
	    'PoZFkF R\\MiM Rf[l[ RZPZS R\\I\\M R^SfSg[ R\\IiIiM RZPkPkS RlXl[ RTDWF RRLUM RaDcF RVSSZ R\\YX[ R^S\\Y',
	    'Po\\ElE R_MjM R_QjQ R_UkU R]ZlZ R_I_Q ReIeZ R_IjIjQ RTDXF RRKWM RWRS[ R\\E\\RZY',
	    'PoaMjM R`PlP R]H][ RaEaL RfMf[ RaEjEjL RhUlY RdT_Y RfOiV RTDXF RRKWM RfObV RWRSZ R^EYO',
	    'PoZFlF R]NiN R[VkV R[ZkZ R[Q[[ R]J]N RcCcG R]JiJiN R[QkQk[ RTDXF RRKWM RWRS[',
	    'Po\\JbJ ReJkJ R\\OkO R\\SkS RZZlZ R\\F\\O RcScZ ReFkFkO RTDXF RRLWN RWSS[ RbD\\E',
	    'PoYHlH R\\UkU R\\ZkZ R\\P\\[ RcPcZ R\\PkPk[ RfHjL R[H^N RaHdM RSDXF RRKWM R]C[I RWRS[ RdCaI RiDgI',
	    'PoZFlF R\\SjS Rd[i[ RcCcF RZOjOjS RhIkL R]HYL RTDXF RRKWM R\\SZWjVi[ RWRS[ ReFeMbM RaG`K]N',
	    'Po[IjI R[NjN RX[l[ R[D[N R`P`[ RePe[ R[DjDjN R[R]X RTDXF RRKWM RjRhX RWRS[',
	    'PoZGkG RYLlL R^WgW Rf[k[ R[O[[ R^R^Y R_C_K RcLcW RfCfK RgRgY R[OkOk[ RTDXF RRKWM RWRSZ',
	    'PoZFlF RZPkP RZKZN R_C_I RcMc[ RfCfI RZKkKkN RUDXG RSLUN RdRkY RcPeS RaRZY RWRSZ',
	    'PoYGdG RZQdQ Rg[l[ RZKZQ R_C_[ RZKdKdQ RgFgU RlCl[ RaTdW R_SbV R_P]TXY RTDWF RRKWM RWRSZ',
	    'Po\\IjI RYLlL R\\E\\I R\\O\\W RcCcL R\\OiOiW R\\EjEjI RTDYF RRKWM ReXk[ RWSTZ RbX[[ RcRbW',
	    'Po[GjG R^KiK R[SjS RYWlW R[D[G RcCcG RcPc[ RjDjG RTDXF RRKVM RVSSZ R`JZM R_LeOlQ RhLbOYQ',
	    'PoYEYT RYE_E_T RSDVE RRLVM RfEiG R[WW[ RkQjTcZ R]V_Z RlWk[ RVSRZ RkOaP ReCeLgX R\\H\\R[W RjIbJ',
	    'PoZKaK RXPbP RZ[][ R]K][ RTDWG R]EbJ RfVb[ R]EWK R`SbW RfQjZ RRLVM RiOeY RVSRZ RZSXY ReLhW RcFkFhU RdFgU',
	    'PoZIlI RZSlS RcCcO R\\D^G RiDfG RcHfLlN RTDXF RRKWM RWRSZ RcH`LZN R`P]Vk[ RgTcYZ[',
	    'Poe[j[ R_V`Z RfUhW R\\D]G R[QWV RcUdX RSDXF RRKWM RWRS[ R[VZZ R^NjMiR RZHhHhM RaE[SlRj[',
	    'PobElE RZJ`J RZO`O RbOlO Rh[l[ RZEZX RZE`E`O RdEdO RhNh[ RlWl[ RSDWE RfU`[ RRKVM RhOdW RVSSZ R^R`WXX RiFgT',
	    'PoZKaK RcKkK RZP`P RZUaU RZEZ[ RZEaEaK RcEkEkK RfUk[ RdPgX RTDXF RRKVM RWRSZ RbOkOhUeY`[',
	    'Po]JeJ RfQiQ RX[l[ R[S[[ R`S`[ ReSe[ R[SjSj[ RTDXF R^McO RRLVM RWRS[ RdLaP]Q RZEfEfHkHjQ R]E\\O',
	    'Po\\MiM RZSkS Re[k[ RfMf[ RkXk[ R[G]J ReGkM RkGhJ R^WZ[ R_HYN RiDeG RdCfI RTDXF RRKWM RWRS[ R[E`E^J R_M_S^W',
	    'PoZEkE RcLkL RYNlN R\\ViV R\\ZiZ R\\R\\[ R\\RiRi[ RiJkL RZLaKaM RTDXF RRKWM RWRS[ R]G[L RfGdL',
	    'PoaGkG RXJaJ R`MlM RaSkS R`[l[ R]C][ RfCfM RfOfZ RTDWE R^N`Q RRKVM RVSS[ R]HYT',
	    'Po[FlF R]NlN R^QiQ R^ZjZ R^U^[ RcCcG RdHdS R^JjJjQ R^UjUj[ RTDXF RRKWM RWRS[ R[F[RYY',
	    'Po[DlD R^MjM R^QjQ R`[d[ R^I^Q RdEcI RdQd[ R^IjIjQ RhTkW R_T]X RTDXF RRKWM RWRS[ R[D[PYX',
	    'Po[NiN R[RiR R[ViV R[J[V RbCbJ R[JiJiV R\\E]I RjEgH RTDXF RRKVM ReXl[ RWRSZ R_XX[',
	    'Po[FjF RYJkJ R[QiQ R[M[Q R^U^[ RcCcJ R[MiMiQ RTDXF RRKWM RcQdVhYlZ RWRS[ RdQ`SXW RkTeV RcZ\\Z',
	    'PoYHlH RYOlO RZSiS R_D_[ ReEe[ RZKiKiS RdSkY R_SXZ RTDXF RRKWM RVSSZ RiDbDZE',
	    'Po[HkH R\\MjM RZSlS RcHcS ReVe[ R^C_G RiUl[ RTDXF RRKWM RWRS[ R\\VZ[ RgCfG R`Va[',
	    'Po^HhH R[LkL R[D[H R[DlDlH RhUlY RjQfR ReOiW RTDXF RRKWM RWRS[ RaNZQ RbP_SZU RcSaWZY ReLhU Rc[_[ RcRdWdZ',
	    'Po\\GkG R\\JjJ R\\MjM R\\QlQ RRUlU R\\F\\Q R_R_[ RdEdQ R^DXJ RXMSR RTDYE RRIWI',
	    'PoYIlI RfRkR RcEc[ RgKgR RTDWE RRLVM RbRfVlX RVSSZ RYM^L^QYQ RkMfO RbR^VXX RiD]E',
	    'Po[GjG RYKlK RZPjP RX[l[ R[S[[ R`S`[ RbCbK ReSe[ R[SjSj[ RgMkQ RTDXF RRKWM RWSS[ R`K^O',
	    'PoZFjF R[JjJ RXNkN R_[b[ RbPb[ R]R_U RfMkS RhReU R_MYS RdJgO RSDXF RRKVM ReWkZ RVSSZ RbF]P R`WYY RbD_N',
	    'PoZGkG R^PfP R\\ZiZ R\\S\\[ R^C^I RfCfI R\\SiSi[ RTDWF RbJeLlP RRKVM RVSSZ RbI^NXP',
	    'PoZFZI R\\NbNbY RZFkFkI RTDWF RbCdF RRLWN RdHkK RWSS[ RaH[K ReUgZ`Z RgMiX R\\N\\S[X RiMaN',
	    'Po\\UkU R\\ZkZ R\\E\\M R\\Q\\[ RcQcZ R\\QkQk[ RTDYF RRKWM RXRTZ RcElElNhN RbD\\E R`IbLZM RgEfLbO',
	    'PoZFkF R[IjI RYMlM R\\SiS RXWlW Re[i[ R\\O\\[ R_C_L RbMbW RfCfL R\\OiOi[ RTDXF RRKWM RWRS[',
	    'Po\\UjU R^ZkZ R[ZhZ R\\P\\U RcMcZ R\\PjPjU RhWl[ RbFcH RTDYF RRKWM RXRUX R]EaJfLlN R[EiEdJ`LYN',
	    'Po\\LiL R\\PiP RZTlT RZEZH R\\I\\P R\\IiIiP RZEkElH RiXl[ RfWiX RTDXF RRKWM RWRS[ R_W\\YY[ RcPcT',
	    'Po[GjG R[KjK RYPlP R^TiT R\\[l[ RbGbP RcTc[ R^C^F R[WX[ RgCfF RTDXF RRKWM RWRS[ R^QZY',
	    'PoaGkG RaLlL RYM^M RaQlQ RYV^V Re[i[ RYEYX RYE^E^V RfCfL RiLi[ RSDVG RbSdU RRLUM RUSRZ',
	    'Po]JgJ RX[l[ R[T[[ R`T`[ RdTd[ R[TiTi[ R`NgNlS R`EfEkJ R]FYJ R]OYT RTDXF RRKWM RVSS[',
	    'Po`KhK R^TjT R[H[[ R^N^[ RbNb[ RfNf[ RSDVG RSLUN RdD]J RdDgHlJ R\\EWO RUSRZ R^NjNj[h[',
	    'Po\\ElE R\\IkI R\\MkM Rf[k[ R_T_Y RcEcR RfTiW RcTdX RTDXF RRKWM RWRT[ R[TYZ R\\E\\RlRk[',
	    'PoZGlG R[MjM R[PjP RYTlT Rb[f[ R[J[Q RcCcQ RfRf[ R[JjJjQ RRKWM R]V`Y RTDXF RWRS[ RfDjE',
	    'PoYKaK RYNaN RYFYJ R]C]J RZR`R`X RaFaJ RhWlZ RSDWE RRKVM RgUb[ RfPjY RiOeX RVSSZ RcV_X RdJhW RfCcM ReIkIhU RZRYY',
	    'PoYEkE RZOjO RYSlS RZIZO R`E`O RbOb[ ReEeO RZIjIjO RTDXF RRKWM RbRhXlY RWRSZ RbR]XXY',
	    'PoYGcG RXLlL RYPdP R^C^L R^M^[ RSDVF R`ScV R^RaU RgEkI R\\TXX RhUa[ RRKVM RjOfW RVSRZ R^O[V RlWk[i[ RfCeMgY',
	    'Po\\KiK R\\OlO Re[j[ R_U`Y R\\FiFiK RfUhW RcUdX RbCbF RTDXF RRKWM RWRS[ R[VZZ R\\F\\SkSj[',
	    'PoZRkR RYUlU RgNkR RgFkJ ReK_Q RaJ]M R]EbFbI RTDXF R\\MaO RRKWM RbUfYk[ R\\EZJ RWRS[ RbS`W^YY[ RiD_E',
	    'Po\\NiN RX[l[ R[R[[ R\\E\\N R`R`[ ReRe[ R\\EiEiN R[RjRj[ RTDXF RRKWM RWSS[ RcGcJ^L RcJgL',
	    'PoZGkG R[LjL RYPlP ReWjW Rc[k[ RdPdZ RiTiW RlWkZ R^D_G R`TY[ RgDeG RTDXF RRLVM RgReW RWRS[ RbGbP^W RbJaT',
	    'Po[HjH R[LjL RdPkP R[D[L RbDbL ReLeP R[DjDjL R`PZW RTDXF RRKWM RWRS[ R_L^PYQ R^TaWeYl[ R^ThSdW`YY[',
	    'PobDlD RZM`M RZX`X RZGZ[ R\\C[G`G`M RZQ`Q`Y RcIcW RgDg[ RSDWE RRKVM RVSSZ RcIkIkViV',
	    'PoYIcI RfKlK ReRlR RZSbS Rh[l[ RZLZU RbLbT RfDlDl[ R]WY[ RZC\\G RTDWF RRKWM RWSS[ RbC`H R^I^R]X RfDfRdX',
	    'Po[IjI RYLlL R\\UiU RY[l[ R[E[I R\\O\\U RbCbI R\\OiOiU RjEjI R^V^Y RTDXF RRLVM RgVe[ RWRS[',
	    'PoZFkF R[JjJ RXMlM RZTiT RbPb[ ReMkS R^NXT RdJgP RSDXF RRKVM RbTfXjZ RVSS[ R`H\\Q RbT]YYZ RaD^N ReP]Q',
	    'Po[J`J RfJkJ R[N`N ReNkN R[F[N RcCcR ReEkEkN RTDXF RRKWM RWRS[ R_D[F R]SaWfYl[ R[RiReW`YY[',
	    'Po]IiI R\\OjO R\\SjS R\\WjW R\\L\\W R]D]I R]DiDiI R\\LjLjW RTDXF RRKWM RfXl[ RWRS[ R`XZ[',
	    'Po[JiJ R[MiM RYUkU R[FiFiQ RbCaF RTDXF R[F[QgQiS RRKWM RWRSZ RbU_XX[ RbScWfYl[ RbQaW',
	    'PoZJaJ RZNaN RZRaR R\\[_[ R\\C[FaFaN RdDdK RSDWE RfVk[ RiUc[ RdQgX RRKVM RcPjPgX RVSSZ RlJlM RdDiDkMiM RZFZRXY R`S`Z',
	    'Po]ZiZ RZGZJ R]T][ RcCcG R]TiTi[ R[GkGkJ RfIjM R_I[N RTDXF RcMhRlS RRKWM RWRS[ RcM\\SYT',
	    'Po]HjH R[ZlZ R[D[[ ReOeV R[DlDl[ RcMgQjT RSDXF RRKWM RXRTZ RdH`L]M RcMaO]Q RhKeObS]U RdW`W',
	    'Po[FkF R[IjI RZLlL R[PcP RZTkT R`[c[ RcCcL RcTc[ RgVkZ RhQkU R]VZZ RTDXF RRKWM RWRS[ RbM[P RfOZT',
	    'Po[FlF R\\PdP R[TkT R`[c[ RZJZN RcTc[ RZJlJlN RhVlZ RhQlT R^VZZ RcCcK_N\\O RTDXF RRKVM RWRS[ RfO[S',
	    'Po[JaJ ReJlJ R\\[`[ Rf[k[ RZDaDaJ RdDlDlJ R^TZY RgRdT RiTdX R]QZT RSDWE RRKVM RWSS[ ReJdPlOlVk[ RZJZPaOaV`[',
	    'Po^HiH R^KiK R^[a[ Ri[l[ R\\D\\M RYPaPa[ R\\DkDkM RdPlPl[ R^P[S R]WXY R`S[X RiPeT RhWcY RjSfX RTDYF RRKWM RWRS[',
	    'PoYElE RYQaQ RcQkQ R[UjU RY[l[ RcRc[ R_MaQ RjMkQ RcIhLcP R]F[J^LZP RgFdJ RTDXF RRKWM RVSS[',
	    'Po[DlD R]GjG R[JlJ RYTlT Rb[f[ R^J^P RgQg[ RTDXF RdKgOlQ RRKWM R]VaX RVSSZ RjLfN R[D[KZP RdP]Q',
	    'Po[KkK R[PkP ReWkW R[F[P R[FkFkP RcPc[lZl[ R`UZ[ RiTkW RTDYF RRKWM RgReW RXRUY RbP^X RbCbFaT',
	    'PobMkM RYN_N RaSlS RYW_W RYEYY R\\E\\W RYE`E`W RfCf[ RiElK RTDWF RRKVM RWSSZ RcEaK',
	    'PoZFlF RYPkP R[VjV R[ZjZ R[R[[ RcRcZ R[RjRj[ RhMkP RgHcL RbE^J RTDWF RRKWM RWRS[ RbCbG R[IbM]O',
	    'Po`KjK R_PkP R[V]V Rb[e[ RYDY[ ReKe[ RiTkY ReClJ RdE^J RSDWF RRKVM RVSSZ RaS^Y R\\K]S RYD]D\\M',
	    'PoZFlF RZQlQ Rc[h[ RZLYO RcCcG RcMcQ RZLlLlO R_VZ[ R^H_K RTDXF RRKWM RWRTZ R`R]Y RgHfJ R_UiUh[',
	    'Po[LiL RcPjPj[ RfJjM R]PYT R\\WXY R_SZX RgPcT RiSdY R^DZH RTDXF RRKWM RaEfDhGlH RWRSZ R`G^L RYP`P`[][ Rg[j[g[',
	    'Po_IfI R\\OiO R\\RiR R]ZiZ R\\L]U][ R\\LiLiR R]UiUi[ RTDXF RRKWM RcChIlJ RWRS[ R\\TY[ RbC\\IYK R\\O[X',
	    'PoZHlH R\\LcL RhCjF RTDXF RRKWM RWRSZ R]O\\T RbObS RZHZRYY R`L`S\\W R_ScV RjMgUb[ RfDeLgZk[lW',
	    'Po[ElE R]LjL R^ThT R[O[[ R]H]L RcTc[ R]HjHjL R[OlOl[ R_QbS RgQdT RTDXF RRKWM RXRUX Rh[k[h[',
	    'Po\\HkH R_MiM R_QiQ R_TiT R[VlV R[L[V R_J_S R_JiJiS RTDXF RRKWM RfXl[ RWRSZ R`XY[ R\\C\\H RhD\\E',
	    'Po[FlF R]MiM R_XgX Rh[k[ R[P[[ R]I]M R_S_Y RcCcF R_SgSgX R]IiIiM R[PkPk[ RTDXF RRKWM RWRTZ',
	    'Po[HjH R[LjL RYSlS Re[k[ R[D[L RcLcS ReSe[ R[DjDjL RlWk[ R]N^Q RiNgQ RTDXF RRKWM RWRS[ R`T^X]YX[',
	    'Po\\GkG R]IjI RYSgS R`N`[ RlWl[ R[O\\R R`SZY RdOcR RTDXF RRKWM R`UeW R]C[I RWRS[ R[LiLiUj[',
	    'PoYHlH RbZlZ RXZaZ RYO]T RbPfT R]C^G R`Ua[ RiUl[ RTDXF RRKWM RjNdZ RWSS[ R]JZQ R`NZZ RhCfG RfJcP',
	    'PobGlG R`SlS R[H[[ R^G^V RfOf[ RSDWE RhUlY RdT_Z RfQgTiW RcHgMlO RRKVM R]CXN RVSSZ RcC`J RiFhJeM`O',
	    'Po[IjI RYMlM R[UjU RYZkZ R[D[H R[P[U RbMbZ R[PjPjU RjDjH RgWl[ RTDXF RRKWM RWRS[ RbJ]K RbDbJ',
	    'PoUR\\R R[[_[ RSMSQ R_O_[ RSMkMkQ RkEhG RUETH R]E[G RaE`H RXH\\J R_OaTeXkZ ReHlK RdI_K R[TWYSZ RgRbT RWIRK RYCXI ReCdI',
	    'Po\\PgP RYTkT RX[l[ RbPb[ R\\V^Y RhEkH RhVeY R^DZH RTDXF R^GbKkO RRKWM ReDiF RVSS[ RfGaLXP',
	    'PocGlG RbMlM RYSlS RbOb[ RcIcM RhDhP R\\J`K[P RTDXF RRKVM RbRhXlZ R]D[J RWRSZ R\\GbG^M RbR]XYZ',
	    'PocHiH R]SiS R]ViV Re[i[ RZLZO R]O][ R^D^L R^DiDiL R]OiOi[ RZLlLlO RTDXF RRKWM RWRTZ RcHbL',
	    'Po]MeM RZPlP R\\XeX Rd[i[ R\\S\\Y R]H]M R]HeHeM R\\SeSeX R[EjEi[ RTDXF RWRSZ RRLWM',
	    'Po[KkK RYQlQ R[UkU RZFZI RcCcF RcHcK RcQc[ RZFkFkI R^M_P RTDXF RRKWM RgLfP RWRS[',
	    'Po\\TaT ReTkT R\\ZkZ R\\O\\[ ReNkNk[ RbGcK RiFlK RTDXF RRKWM R]EZL RWRS[ RbM[N RiC^E',
	    'Po^IiI RTKZK R[MlM RTRZR RV[Z[ R`[c[ RTDZDZ[ RdOd[ R^RaT RgMlR RhDlI RaM[R RiQfT R_E[H RfIiP RfVjX RbG_P RaV\\X RcC`N RTDTSS[',
	    'PoYFkF RXMlM R[QhQ R[I[Q R]T][ R[IhIhQ RaDcF RTDXF RRKVM RcQdUhYlZ RWRS[ R`SXV RjSeU RbY\\Z',
	    'PodGlG R[GbG RaPhP R[QaQ RcTjT R]UcU RZUaU Ra[k[ RbLbZ RhFhI RkXk[ R]F_J RTDXF RRKWM R\\CYJ RVSS[ ReDcI RhK]M RdP]P RgTaU',
	    'Po\\MjM RZQlQ R\\ZjZ R[EZH R\\T\\[ RcMcQ R\\TjTj[ R[EkEkH RTDXF RRLVM ReHkJ RVSSZ R`HZJ',
	    'PoZElE R^KjK R_NjN R_RjR R_VkV RZYlY RZEZ[ R_J_W ReIeV RTDXF RRKWM RWRS[ R`G]M',
	    'PoZFlF R]PiP R[QfQ R^T^Z RbCcF ReMiQ R`IfIkM R]IYM RSDXF RRKWM RbPdVhYl[ RWRS[ R`L^P RbP_TXW RjTeV ReY[Z',
	    'PoZF`F R\\OjO RYSlS R[ZjZ R[U[[ RbNbZ RjUj[ R[H\\K RaGbK RTDWF RRKWM RiGgL RWRSZ R\\LZQ RiD_E',
	    'Po\\JiJ R\\MiM RZQlQ R]WhW R\\F\\M RcSc[ R\\FiFiM RfQkW RbD`F R`QZV RTDXF RRKWM RWRS[ RaM^T',
	    'PoZFkF RYKlK R[RjR R[VjV Rb[k[ R[N[X RbCbF RcNc[ R[NjNjV RlXk[ R^G^K RTDXF RRKWM RWRS[ RfFeJ',
	    'PoYGlG R]KhK RfYiY RZNYQ R\\R\\Y R]C]K RbCbK RbOb[ RhChK R\\RiRiY RZNkNkQ RTDXF RRKWM RVRSZ',
	    'PoXH`H RXM`M RbRkR RXT`T Rc[k[ R\\C\\[ RcKcZ RgKgR RbKkKkR RTDWF RRKUN RlWk[ RdGjGhK ReCbJ RURSZ',
	    'PoYEkE R\\HhH R\\KhK RZOkO R\\E\\N RhEhO RSDWE RRKVM R\\U_Y R_TYZ RcQeVhYlZ RYT\\U RVRRZ RYR`Q^W RbQjQiUeYa[',
	    'PoYFlF RYJlJ Rf[k[ RZNZ[ R_C_H R_QaU RfCfH RZNkNk[ RTDXF ReRhU RRLVM RWRSZ R`J_U ReKdV',
	    'PoYFlF R\\OiO R\\TjT RY[l[ RZKZN R_C_I RcOc[ RfCfI RZKlKlN RgViY RTDXF RRKVM RWSS[',
	    'Po[EkE R^HjH R[LkL R^ZjZ R^T^[ R_L_R R^TjTj[ RkNgO RTDXF RcLgPlQ RRLWM RWSSZ RdQ_Q R[E[QYY',
	    'PobFkF R_MfM R`[h[ RaUa[ RbCbH RhXh[ RbKbPjOjP RcSeV RiUkZ RTDXF RRLWN R[IkIjL RWSSZ R^U[Z R[I[RYZ RbM]M',
	    'PodHlH RX[l[ RZRZ[ R[E[N R_C_O R_R_[ ReRe[ RZRjRj[ RSDWF RRLVN RgLjO RWSS[ ReDbL',
	    'PoZFlF RYRlR RbOb[ RcCcG ReFeO RhIlM R]IYL RTDXF RRKWM RbRhXlY RWRSZ R^O\\O RbR\\XXY R`F_M',
	    'PoYGbG RhWl[ RSDVF R[P`V RRLVM R_JaL R\\D]F R]UXZ RfVb[ ReOjY RiOeY RVRRZ R[JYN R`O\\W RdKhV RfCcO ReIkIhU',
	    'Po^KeK R^OiO RZUlU R[G[J R^K^U ReOeU R[GkGkJ RcCdF RTDXF RRKWM RgXjY RVSSZ R`XY[ RhJaJ',
	    'PoXG^G RaJlJ RbOkO RbTkT RbZlZ RbHb[ RhIhZ RgDhH RZN^W RSDWE RRLVM RVRSZ R]NYX RdC`K R^I[U',
	    'PoaElE RX[\\[ R\\M\\[ RbIbU RbIkIkU RYH]L RgWl[ RSDVF RRKVM RfWb[ RXE_E[J RVSRZ RWNaM_R RgEeI RgLgSfW',
	    'PobFlF RbSjS RbVjV Rg[j[ R[H[[ R^G^W RbOb[ RbOjOj[ RbGfKlN RSDWE RRKVM R]CXM RVSSZ RcCaI RiGeL`N',
	    'PoYElE R[KkK R^PiP R]WjW R^[k[ R^S^[ R^M^P R^MiMiP R[GkGkK RlXk[ RTDXF RRKWM RVSSZ RcScW R]SjSjW R[G[RYY',
	    'Po[HjH R[LjL RYPlP RZTkT R[WkW R[D[L R^M^T RcDc[ RhMhT R[DjDjL RTDXF RRKWM RWRS[',
	    'Po^FdF R^QcQ Ra[l[ R^J^Q R^C^I RbSb[ RlXl[ RTDWG RRKUM RkEfJ RkKfP RlQeX RVSSZ RYIdIdK R]S\\Y RYIYSXZ RbL[M',
	    'PoYGlG R`KdK ReYiY RYNYQ R\\R\\Y R`C`K RbNb[ RdCdK R\\RiRiY RYNkNkQ RhChLlLlJ RRKWM RSDXF RVRSZ R\\D\\IYL',
	    'Po\\FkF R[KkK R]QiQ R]TiT Re[l[ R]M]T RfTf[ R]MiMiT RlXl[ RcCcF R`G_J RTDYF RRKWM RXRUY RfGfK R_YZ[ R`T_Y',
	    'PoZKaK RfLkL R_[k[ RYFYI RYP`P`Z RfIfO RYFkFlI RSDVF RRKVM RbCcF RiSdX RdRkXl[ RcPjPgU RVSRZ R[PZY',
	    'PoZHiH RdMkM RXM_M^K RjKlM RgEjI R`C\\G RTDXF RbJgPlQ RRKWM RVSSZ R[IZM RgIeN RbJ\\PXR RcP\\S RfS\\W RiVcY[[',
	    'PoZFlF R_WfW R[L[[ R_R_Y R_RfRfW R[LkLk[ RdNhQ RaDdF RaN]R R_H_K RTDXF RRKVM RWRSZ RfHeL Rg[j[g[',
	    'PoZFlF R]OhO R_XgX Rg[k[ R[K[[ R_S_Y RcCcF RcKcS R_SgSgX R[KkKk[ R^F_J RTDXF RRKWM RWRTZ RgGfK',
	    'Po`QhQ R\\ZjZ RZFZI R\\T\\[ R`K`Q RcCcF RcScZ RhOhQ RjTj[ RZFkFkI RaHdI RiKlO RTDXF RRKWM RWRSZ R\\KZO RhIcNZR',
	    'PoXGbG RYKbK RXObO RgVl[ RSDVF RgVb[ ReQiY RRKVM RUSS[ R`[\\[ RfCdLgV R]D\\OZY R[SaS`[ ReIkIhV',
	    'PocGlG R[ZlZ R[K[[ RcCcK R[KlKl[ RcVeX R]R_T R^ObPhW RbMdO RhQjS RgO^W RTDYF RRKWM RWRTZ',
	    'PoYD_D RcHlH RXHaH RYL`L RYO`O RaOlO RYY`Y RYSY[ RYS`S`Y RgHg[ RSDVF RRKVM RVSRZ RdCbL',
	    'PobEjE R^LfL RbNkN R[VlV R_[c[ RbJbN RbCbH R]QdQd[ R^R_U RhRgU RTDXF RRKWM RZHlHkK RWRS[ RZHZRYZ RiPbQ',
	    'Po_[g[ R\\F\\R R_U_[ R_F_O RgYg[ RTDWE RhUlY RfMkR RhLcQ R\\GXK RcGgO RRKVM R`SdU RcGkGgN R]C[I RVSS[ R[VY[ RdCaJ',
	    'Po[FlF RZLlL R^QkQ R^UjU R[[l[ RcCcF RcNc[ RTDXF RRKWM RWRS[ R_N\\T R^GhJ RgGbI\\K RZLZSYZ',
	    'PocGlG RZGaG R^D^O RgDgO R]QgQgZlYlZ R`TcW R^IaJ RgGlM ReIbM R\\IYM RTDWF RRKVM RWSSZ R^F\\K RgFdK R[ZY[ R]Q\\X',
	    'PoZFlF R\\LlL R]OiO R]UjU R]XjX Rg[j[ R]R][ RcCcG Rc[ R]IjIjO R]RjRj[ RTDXF RRKWM RWRS[ RZFZRYY',
	    'PoZFlF R\\RhR R\\M\\R R_U_Z RbCbF R\\MhMhR R_IfIkN R]JYO RTDXF RRKWM RcRdVhYl[ RWRS[ RaSXW RkUfW ReY[[',
	    'PoYFlF R^JgJ Rg[k[ RZNZ[ R^C^J RbJb[ RgCgJ RZNkNk[ RTDXF RRKWM RfRdW RVSSZ R^R\\W R\\Q^Q`W ReQgQhV R^Q\\Q RfQdQ',
	    'PoSE_E RSH^H RhIlI RTN^N RZ[_[ RYCYH RTJ^J^N R_Q_[ RbDiDiH R_RdXk[ R`KjKgMaP RST\\TXXSZ RiSbV RYJYN RaMjO RbDaH RTJSR',
	    'Po_W`[ RdVe[ RjWl[ RTDXF RRKWM RWRS[ R[VY[ R^FfFcJ R_D]GYK R\\JjJjT\\T\\J RdJdT R\\OjO',
	    'PoYDlD R[MjM R[QjQ RYTlT R_[b[ R[H[M R`D`M RbTb[ ReDeM R[HjHkM RiWlZ R]VYZ RTDXF RRKVM RWRSZ',
	    'Po^LbL RYPcP R[[^[ RYFYI R^H^[ RYFlFlI RgVl[ RbCcF RhUb[ RaScW ReQiY RTDWE RRKVM RkNfX RVSSZ R[SYX RcKkKhU RdKgV',
	    'Po[HkH R[KlK R^OjO R\\SkS R]VjV R[ZlZ RcCcH RdLdR RdTdZ RkDkH RTDXF RRKWM RWRS[ R[D[H R[KZSYZ',
	    'Po\\MjM R\\QjQ RZTlT R\\J\\Q RcJc[ R\\JjJjQ R[F^H RaFdH RhFjI RjChF R]D[F RcCaF RTDXF RRKWM RcTgXlZ RWRS[ RcT_XYZ',
	    'PoZGlG R_[b[ RbQb[ R\\S_U RhRfU RTDXF RbCbLeOlR RRKWM ReWkZ RcGfJlL RWRS[ RcG_IYL RbL_PYR R`WZZ',
	    'PofGkG R^MlM ReSkS Re[l[ R\\L\\[ RaEaM RfCfZ RbWeZ R^DYI R\\MXP RaRbX RTDWE RRKVM RVSSZ R]H[O RaP_Z',
	    'Po^OgO R[SjS R^[b[ RbSb[ RYJ]K RRKWM RgVkY ReKkP RiIfL R\\VYY R_JYQ R\\FXJ RcEgM RSDXF R[H`J ReFkEhJ RVSSZ R\\FaF^M',
	    'PoZFlF RZPkP R]TbT Re[l[ R]Q][ R`H`O RbCcF ReQe[ ReHeO RZKkKkO RlXk[ RTDXF RRKWM RVSSZ RkSdV RbY[[ RZFZRXY',
	    'PoYHlH R\\KiK R[QjQ RYTlT R[N[Q R\\E\\K R\\EiEiK R[NjNjQ RbCbQ^T[X RTDXF RRKWM RWRS[ R\\Wl[ RhTcYY[',
	    'PoaNjN RaSkS RaWjW R_ZlZ RYDY[ RfPfZ RTDWG RRLUM RbP`U RdE_I R\\K^Q RcFgKlM RVSSZ RYE^E\\L RcFkFfJ_M R^T[T',
	    'PoYFlF R^IfI R[PjP R[SjS R[WjW RY[l[ R[L[P R^C^I RbIb[ RfCfI RTDXF RRKWM R[LiLjP RWSS[',
	    'Po\\JjJ R]QbQ RZZlZ RZEZ[ R]M]Q R]MbMbQ RZElEl[ RgGiH RgRaX RTDXF RRKWM RiLeT RWRSZ RcT\\U ReGdLfU RjUjXhW',
	    'PoeSkS RdZlZ R\\K\\[ RfNfZ RSDWE RaWdZ RbGdJ RhHlL R\\DXI R\\LXP RRKVM RWSS[ R]H[N RbD`L RhDfK R`QaW R`P_Z',
	    'Po[IkI R[LlL Rh[k[ R]P][ RdLd[ R[DkDkI R]PkPk[ RfVhX R_RaT R_VaX RfRhT RTDXF RRKWM RVSS[ R[D[QYY',
	    'PoSE^E RRH_H RRK_K RSN^N RZ[_[ RYCYH RYKYQ R_Q_[ R`FiFiO RcK_O R[H[K RUIVK R`JfL R_RdWkZ RSS[TWXRZ RiSbU RdEcL RlMlPjP',
	    'PoYJ`J RYO`O RbOlO RYSaS RYWaW Rh[l[ RYFY[ RYF`F`O RdEdO RhOh[ RlXl[ RSDWE RRKVM R\\C\\F RVSSZ RjEhOeWa[',
	    'PobJjJ RZK_K RaTkT RaZlZ RYZ]Z RYE_D_J RaNaT RbDbJ RfJfZ RbDjDjJ RaNkNkT RSDVF RRKVM RjVl[ RVSRZ RZKYQ^Q]Z',
	    'PoZFlF R\\OjO R]WhW Rg[k[ R[R[[ R\\H\\O RcCcF RjHjO R[RkRk[ ReTiX R_IgM RTDXF RRKWM RWRS[ ReHcK^M RbO_W',
	    'Po]JjJ R\\RjR R\\VjV R\\N\\V RcCcF RcKcV R\\NjNjV RTDXF RRKWM RfXl[ RWRTZ R`XY[ R[I[FlFlI',
	    'PoZFlF R[MkM R[QkQ R\\WjW R\\ZjZ R[I[P R\\S\\[ R`C`P ReCeP R\\SjSj[ R[IkIkP RSDXF RRLVN RVRSZ',
	    'PoXMbM RZ[][ R]S][ RhWl[ RSDWE RgUb[ ReQiY RYEbH RRKVM RiPeX RUSS[ R`C]GXI RdJgU RdIkIhU ReCcN R\\JYQaP^UXV RbUZV',
	    'Po[RjR R^[h[ R[F[R R^U^[ R[FjFjR R`TdU RjUlY R`LeN RiXh[ RaC`F RaH^K RTDXF RRLWM RWSS[ RZUYZ R`JfJbN_O',
	    'Po[DlD R^GjG R[JlJ R]TjT R]WjW Rf[j[ R]Q][ R_J_O R]QjQj[ RTDXF RcJfMlO RRKWM RWRS[ RjKfM RdN]O R[D[PYY',
	    'PoZFlF R[LjL RZRkR RYUlU Rc[g[ RcCcR RhRh[ R[HjHjO R]V`Y RTDXF R[H[OiOkR RRKWM RWRS[',
	    'Po[EkE R`MhM R^VcV RfVkV R[YlY R[E[[ R^P^V R`H`M R^PcPcV RfPfV R`HhHhM RfPkPkV RTDXF RRKWM RWRS[',
	    'Po\\IhI RYMlM R^[b[ RbPb[ R\\R^T ReMkR RfDkI RhReT R]FYI R_MYS RdIfO RSDXF RRKWM RdWjY RWRSZ RaG\\P R_VYY RaC^N',
	    'PoZGbG RcGlG R[UjU RY[l[ R^C^P RbRb[ RgCgP R]HaL RgHkM RTDXF RRKWM RWRS[ R^FZN RgFcN',
	    'Po]UkU R]ZkZ R]Q][ R[DbDbM R]QkQk[ RdDlDlM R[G^H ReGiH RcNcQ RTDXF RRKVM RWRSZ RbIZM RlIeL',
	    'Po[FbF R[OjO R[RjR R[UjU Re[l[ R[L[U ReUeZ R[LjLjU RlXl[ RTDWG R\\H]J RiFhJ RcGbJ RRLVN RWRSZ R_W]YY[ RjD`E',
	    'Po]QiQ R\\UjU RZ[l[ RZHZL R]L]Q RcCcH RcQc[ R]LiLiQ RZHlHlL R]D^G RiDhG RTDXF RRKWM RWRS[',
	    'PoZFlF R[MiM R[QiQ RYUlU R[J[Q R_C_H RfCfH R[JjJjQ RUDWG RRLVN RbTfYk[ RWRSZ RbR`W^YY[',
	    'Po\\FkF R]KiK R]NiN RYQlQ Rf[i[ R]H]N R]HiHiN R_QYW RaU[[ ReWa[ RTDXF RRKWM RfTdY R]C[H RWRSZ R\\TkTj[',
	    'PoYFlF R[OiO RZRkR RYUlU R[J[O R_C_H RfCfH R[JiJiO RTDXF RRKWM RbJbUeYk[ RWRSZ RbU_YY[',
	    'Po^FkF R_NjN R_QjQ R]UlU R_J_Q RdCdY R_JjJjQ RSDWE RYJ\\M RYD\\F RXQ[Q[XX[ RRKVM RWSS[ R[Xa[l[',
	    'PoYGaG RcGlG RYRlR R^C^O RgCgO RTDWG R\\GaK RfGlM RfIbM RRLWM RVSRZ R]FZM RgFdK R`O^S\\Vk[ RgSbX]ZX[',
	    'Po\\NjN RZRlR R[VkV RY[l[ RZFZI RcCcF RcLcR RcTc[ RZFlFlI RTDXF RWSS[ R`F^JZL RRLWM ReFeKkJkK',
	    'PoTE^E RbGlG RSI^I RUPkP RYCYN RWKRM RYHUL RbGeLlN RYJ^L R`ReWkZ RcCaH RiGeK_N RWT]TZXVZ RhTcU R`Q`[][ RUQTY',
	    'PoYGbG ReHlH RZOaO RZSaS RXVbV RdZkZ RZKZR R]S][ R]C]K RZKaKaR RkVkZ RSDWG RRLUM RdMjMdY RVSRZ RfCdK',
	    'Po`PkP RaSjS R_VlV R`[l[ ReNeY RdD_I RZE[I RbGfKkM RTDXF RRKVM RYN\\N\\Xb[ RWRS[ RcFiFeK_N R\\XXZ',
	    'PocHlH RXHaH RgRkR RSDWE RcTeY RRKVM RVSSZ RdCbJ R^[[[ RgLfZl[ RbLkLjP RcPaZ R\\C\\H[OYX R[M_M_U^[',
	    'Po`FlF R`WfW RY[\\[ Rf[j[ R`Q`X R`QfQfW R_NkNj[ RSDVF R\\FXK R]NXS RRKVM ReHiJ RUSS[ RdJ_L RZE\\J]N]V\\[ ReDeI',
	    'Po\\GjG R\\JjJ R[QkQ R[M[Q R\\D\\J R\\DjDjJ R[MkMkQ RTDXF RRLWN RWRTY R`M`Q ReMfQ R\\S_WdYl[ RZTjTgVbYZ[',
	    'PoZEkE R[HjH RYKlK R\\PjP R\\TjT R\\WjW R\\M\\W RcCcK R\\MjMjW RTDXF RRKWM RfXl[ RWRS[ R_YX[',
	    'PoYFlF RZKkK RYTkT R_C_H RfCfH RfQf[ RUDWG R_Q^W[[ RcKfOlQ RRKWM RdIhJ RWRSZ RbH`M^OYQ',
	    'Po[FlF R]MlM R^QjQ R`[c[ RdDd[ R^JjJjQ R^S`U RTDXF RRKVM RjSdTgXlY RWRS[ RcV\\Y R[F[TZZ',
	    'Po[HjH R[LjL RYUkU R[D[L RbUb[ R[DjDjL RhWkZ RhRlV RbDbL^O R]WZ[ RTDXF RRKWM R[PaR RWRS[ RfNbS]T',
	    'Po[HlH R^[a[ Ri[l[ R[D[H R[LaLa[ RcCcH ReLlLl[ R\\S`U RgSjU RTDXF RgOjP R]O`P RRKWM RWRSZ RlDlH R[L[SYY ReLeTdZ',
	    'PoYGbG RYQbQ RYKYQ R]C][ RYKbKbQ RSDVE R]RbW RRKVM RVSSZ ReHkHjN ReCcN R^Q[UXX RgIgRfWb[ RgRiUl[',
	    'PobDkD RbHjH RYK^K RbLjL R`PlP RY[][ RYD^D^K RYKXQ^P^Z RbDbZ RgWl[ RSDWE RjSgV RePiY RRKVM RVSSZ RgY`[',
	    'Po[FkF RZJlJ R\\PjP R\\SjS RYWlW R\\M\\S RcCcF RcSc[ R\\MjMjS R_F_J RTDXF RRKWM RWRS[ RhFfJ',
	    'Po[HjH R]PiP RZSlS R_[c[ RZLZO R[D[H RcCcH RcSc[ RjDjH RZLlLlO RgVkZ RbJcL R^VYZ RTDXF RRKWM RWRS[',
	    'PoZKaK RXPbP RZ[][ R]K][ RhWl[ RfVa[ R`SbW R]C`FaI RePiY RSDWF RRKVM RiOdX R\\CZJ RWSSZ RZSXY RdJhW ReCbN RdIkIgU',
	    'Po[JjJ RZQkQ R_[g[ RZMZR R[F[J R_U_[ RbCbS RgYg[ R[FjFjJ RZMkMkR RaTeV RhVlZ RTDXF RRKWM RWSS[ R[VYZ',
	    'PoYFcF RZMbM R[[^[ RZIZM R^S^[ RZIbIbM RfEf[ RSDVF R^C_F RYPbP^S RjMlS RRKVM RfElEiM RVSRZ RlVhV RcT^UYU',
	    'PoYGbG ReLlL RYNbN RYRbR RXVbV RYJYR R]C][ RYJbJbR RiLi[ RSDWE RRKVM RVSSZ RlDdFeRcY',
	    'Po[ElE RcIkI RYLlL R\\QkQ Rf[j[ R]H]L R`V`Y RcEcL RgUhX RcVdY RTDXF RRKVM RWRSZ R[VYZ R\\N\\TlTk[',
	    'PoYGlG R[P_T RZJ^L RgPjT R_PZU RjIeM RjPfT R_IZM RTDXF RRKWM RbCbQdVhYl[ ReJjL RVSSZ RbP`V]YX[ RbMaU',
	    'PocDjDjL RhIdL R^IZM RgEdI R]EZI RSDXF RdNlR RRKWM RVSSZ RZD`DaL_OXR RcQ[T RfS]W RhVbY[[',
	    'Po[IjI R[NkN R[RjR R[VjV R[ZlZ R[D[I R\\M\\[ RbCbI RdLdZ RjDjI R\\NXR RTDXF RRKWM RVSSZ R]JZQ',
	    'PoZIlI R]KbK R^TkT R\\WlW RdJdP RdQd[ RZElElI RTDXF RRKVM RWRSZ R_Q]U RkKgL RfNlN RbN\\N RZEZQYZ',
	    'PoZGkG R[JjJ RYMlM R_[b[ RcGcM R\\QbQc[ R]D_F RhCeF RTDXF RRKWM RcSgXlZ RVSSZ RYT_T[YWZ RiTeU R^NfO',
	    'PoaEjE RTHZH RRLZL R\\LlL RdRgR R[[_[ RTDTH RZCZR R_Q_[ RgJgR R_NbP R_FbI RaEeG RST[TXXRZ RiScV RcC\\F R_RdWgYlZ RULTQ RgG^J',
	    'PoZJkJ RZDZI R_D_I RZNbMc[ ReDeI RZDkDkI RTDWG R]T_X RhRkX RRLVM RVSSZ RhPeY R\\PZY RjLbM',
	    'Po_ElE RTL]L R`MjM R`QjQ R`VjV RU[X[ RUCUL RYMY[ R`I`V RfEdI R`IkIkV RhXk[ R[T^X R^PZS RbY^[ R]I]L RYO\\W RSQWQTX R\\ETG',
	    'PoZHkH RYNkN RXZlZ RZDZH R`N`Z RbCbH ReNeZ RkDkH R]J_M RZQ\\W RTDXF RRKWM RjQhW RWSS[ RgJeP',
	    'Po`TjT R`WjW Rg[j[ R\\K\\[ R`P`[ R`PjPj[ RRKVM R]DXH R\\KWP RaGeKlN RTDXF RWSSZ RbC`I RaGiGeL^O',
	    'PoYFbF RYPaP RYJYP R]C][ RYJaJaP RSDWE R]QbV RgVl[ RgVb[ RfRiY RRKVM RiPeX RVSSZ R]OZX RdJgV ReIkIhV ReCcN',
	    'Po]GkG RZKlK R]PkP R]SjS R]VjV R]ZlZ R^N^[ RdNdZ RdLcO RTDXF RRKWM R^C[I R^NXRS[ RbF]P',
	    'PoZFlF R\\OdO R[TjT RZJZN R_C_H RfCfH RZJlJlN RgVkZ RgQkT R^VZZ RbJ]O RSDWF RRLVN RVSSZ ReO[S RcSc[`[',
	    'PoYElE RYMlM RZPjP R_C_G RbGb[ RfCfG R[JjJjP RjRj[ RTDWF RfSgY RRLVM RVRSZ R^S]Y R[RZZ',
	    'Po[KjK R[E[K R`E`K ReEeK R[EjEjK RhUlY RiNfQjX RTDWF RRLVM RVSS[ RbLZO RbPZT RdTZY Rc[`[ RdQdWdZ',
	    'Po[GkG R\\MjM R\\QjQ R\\UbU Re[l[ R[D[G R\\S\\[ R\\J\\Q RfSf[ R\\JjJjQ RkDkG RlXl[ RTDXF RRLWM RWSSZ RkTeV RcCcG RbZZZ',
	    'PofHlH RZKaK RXPaP RZ[][ R]K]Z RfCfM RfVl[ RTDVG RRLVM R]EbJ RhTbZ R]EWK R`SbW RcOhX RbNkNgW RVSRZ RZSXX',
	    'PoZKaK R]O]T RTDWG RRLUM R^EbJ RfVa[ R]EXK RYPZU RfQjZ RhPdY RVRSZ ReLiX RaO_X RbWXY RfCdKgV ReIjIgU',
	    'PoZMkM RaRiR R[RfR RZVjV RX[l[ RYFYI RbRbZ RYFkFkI RTDWG RSLUN RfOjS RaCcF R`M[R ReIiJ RVSS[ R`IZK',
	    'PoaJlJ RbOkO RbTkT RbZlZ RbHb[ RhIhZ RSDVG RRKUN RgDhH R[OYS R\\DXL RVRRZ R^I[P RdD_M R^RXS R]KXL R_WWX',
	    'Po`[b[ RXKX[ R]C][ RXKbKb[ RSDVG RYEZJ RgRkZ RRKUM RiQeZ RVSRZ RaE`I RjLgX R_N`U RfJhW RfIkIhV R[NZU RfCdN',
	    'Po`NcN RfSkS R_YgY Rh[k[ R[P[[ R_U_Z RcIcN RePfS R_UgUgY R[PkPk[ RZIlIhL RTDXF RRKWM RWRTZ R]DiDcG RbJZN RaP`S]T R_FhI',
	    'Po]IkI R]LkL R]PkP R]TlT R^G^U RaWa[ RdHdT ReWf[ RjVk[ RdDdF RTDYF RRKWM R_C[K RXRUX R]W[[',
	    'PoZQ_Q RbTjT R[[^[ RYM_L_Q RcLhLhQkQkP RZG^H RjHgI RTDXF RRKWM RcUfYk[ RVSSZ RiUeYa[ RZQYU_U^[ RcLbR R[DaD^IZL RdDeHgJmL RhEeG',
	    'Po[NjN R\\H\\N RZJjIjN ReSkY RiReS R^EYJ RbMfU RTDXF RRKWM RVSSZ RcNYR RbRYV R_PcTaWYZ Rc[_[ RaQdVcZ R^FeFbJcQ',
	    'PoZFlF R[LjL R[OjO R[I[O RbCbS R[IjIjO RhVlZ RZSiRlT RTDXF RRKWM RaTeV RWRS[ R[VZZ R`V_[gZg[',
	    'PoYFbF RZIaI RiMkM RYUkU R^C^L RbTb[ RdDkDkM RRKWM RiSlV RaM^Q ReO^U R]WZZ RTDXF R]QaR RVSSZ RfEeL RbLYM RgWkZ',
	    'Po]HkH RZNlN R[TlT R]I]T RaIaT RaWa[ ReIeT RiIiT RfWf[ RjWk[ RTDXF RRKWM RWRSZ R^C[J R\\W[[',
	    'PoZDaD RdDlD RZHaH RdHkH RYLaL RcLlL R[TjT R\\[k[ R\\O\\Z R^D^L RcOcT RhDhL R[OjOjT RlWkZ RTDXF RRKWM RWRS[',
	    'Po\\GkG R\\JjJ R\\MjM R\\PkP RYTlT R\\F\\P RcEc[ RTDXF RRKWM RbSgXlZ R]CZJ RWRSZ RcCbG RbS]XXZ',
	    'PoZEaE RYKlK R[ViV R[ZiZ R[R[[ RcDcP RbRbZ R[RiRi[ R\\G]I RTDXF RbJfNlQ RRKWM RWSS[ RhFfK RbJ]OYQ RiC^E',
	    'Pof[j[ RaUaY RgUiW RhEkI RdUeX RbFbI RTDXF RRKWM R\\KgKgO R\\OiOiS R\\EZK RWRTZ R^V\\Z R\\K\\TkSj[ R\\NZX RhC^E',
	    'PoYHlH R[LiL ReQjQ RZTjT ReVjV RZNZW_W_[ RbCb[ ReNeQ ReVe[ R[EiEiL RjNj[ RTDXF RRKWM RVSSZ RZQYZ R_N^QZQ',
	    'PoZFbF RdFlF RZJaJ RcKlK R[VjV R[ZjZ RYHYK R[R[[ R^C^K^O RcHcK R[RjRj[ RhChPlOlP R]MYQ RgLcQ RTDXF RRKWM RVSSZ R^E]N RbO]O',
	    'PoZFaF RcFkF RYJaJ RcJlJ R\\ViV R\\ZiZ R\\R\\[ R\\RiRi[ RTDXF RRKWM RWRS[ R^C^J\\OYQ R^K`N RgCgJfMcP RgJiMlP',
	    'Po^HgH R\\NiN R][a[ Rg[k[ R\\J\\N RYQaQa[ R\\JiJiN RcQkQk[ RSDWE R\\RYU RfRdU RgXbY RiUeY RRKVM RdElH RVRSZ R_U\\XXY RbC_FYI RbDdD',
	    'PoXL_L RcZjZ RXEXL RYOYZ R\\L\\Y RXE_E_L RcQc[ RbRkRj[ RSDVF ReLkR RhKbR RdGfN RRKVM RdGjGgN RUSS[ ReCaK R_Q\\Q R`XWY',
	    'Po\\JaJ ReJkJ R\\NkN Rf[k[ R\\E\\N ReEkEkN RcUdX RfTiW R`NYU R_U`Z RTDXF RRKWM RWRTZ R\\VZZ RaD\\F R]RlRk[',
	    'PoZHaH RdHjH RdLkL RYQkQ RXVlV R^N^V RdDdL RfNfV RdDjDjH RTDXF RRKWM RdXk[ RVSSZ R_XY[ RZD`DaH R[DZL`K`L',
	    'PoXGcG RZNaN RZIZM R]C]G RZIaIaM RhWl[ RSDVF RRKVM RfVa[ RfQjZ RiOeX RUSS[ ReLhX RYQaQ]T][Z[ ReIgU ReCcN ReIkIhU RbU^UYV',
	    'PoZFkF R_JgJ RZLlL R\\SjS R\\WjW R\\O\\W R_C_I RcLcW RgCgI R\\OjOjW RTDXF RRKWM RfXl[ RWRS[ R_XY[',
	    'PoZHaH ReHlH ReLlL RZLaL R]PiP Rh[l[ RZDZ[ RZDaDaL ReDeL ReDlDl[ RcTgX RTDXF RRKWM R_RdT RWRSZ RfPeTbV]X',
	    'PoZHaH ReHlH RZLaL ReLlL R^PhP R^TgT R]XiX Rg[l[ RZDZ[ RZDaDaL RcPcX ReDeL ReDlDl[ RTDXF RRKWM RWRSZ',
	    'PoZElE RZNkN RYTlT RZHZN R`E`N RbOb[ ReEeN RZHkHkN R]P^S RhPgS RTDXF RRKWM RdTgXlZ RWRS[ RbS^XYZ',
	    'PoZGlG R\\PiP R\\TiT R_[b[ R]L]T RcTc[ R[MiLiT RTDWG RSLVO RhWkZ R[I]K RdGkN RjHhK R]VZZ R`HZN RWRSZ RbC^J',
	    'Po[OlO R]VjV R]ZjZ R[G[O R]R][ RdGdO R]RjRj[ R[GlGlO R_I`M R^D`F RiDfG RiJfM RTDXF RRKWM RWRSZ',
	    'PoXLlL RZFZL R_F_L RbOb[ ReFeL RZFjFjL R^SXY R]MYQ RbOdU RTDXF RRKWM RjOgRdShXlY RVSS[ ReM]Q',
	    'Po[HiH R[LiL RZQkQ RYVlV R[D[L R_N_U RbDbL RfNfU R[DiDjL RgXk[ RTDXF RRKWM RWRS[ R^YY[',
	    'PoZFcF RYJcJ ReNlN R\\UjU R\\YjY R\\Q\\[ ReEeN R\\QjQj[ ReElElN R]LYP RTDWF R]LcN RRKWM R[CYH RWRSZ R^F]M',
	    'PoZHjH RYKlK R]QcQ R[UjU R_[b[ RbUb[ RgWkZ RgRjV R\\D^F RfKkP RaM]Q R\\WX[ R`KZQ RhDfF RTDXF RWSS[ RbE^M ReP[T RRLWM',
	    'PoZIaI RdIkI R[OjO R[SjS RYWlW RZDZI R[L[S RZDaDaI RcLc[ RdDdI R[LjLjS RdDkDkI RRKWM RTDXF RVSSZ',
	    'PoYDlD RZKkK R[QjQ R[TjT RYWlW RZGZK R[N[T R`D`K RbTb[ ReDeK R[NjNjT RZGkGkK RTDXF RRKVM RWRSZ',
	    'PoYGcG RfKlK RZNbN RfRlR RZRbR RXVcV Rh[l[ RZJZR R^C^J R^R^[ RZJbJbR RfDlDl[ RRLVM RSDWF RVRSZ RfDfRdY',
	    'PoYGlG R[JiJ RYLaL RcQjQ RYTlT Ra[f[ R]L]Q RcLcQ RfRf[ R[DiDiJ RcLjLjQ RTDXF RRKWM R\\V`X RVSSZ RaPYQ',
	    'Po\\IiI RYLlL R\\QjQ R\\TjT R\\WjW R\\N\\W RcCcL R\\EiEiI R\\NjNjW RTDXF RRKWM RfXl[ RWRS[ R_YX[ R\\E\\I',
	    'Po[FkF R\\IjI R^OiO R]ViV RZ[l[ R[L[O R]R]V RcCcI R]RiRiV R[LkLkO R`W`[ RTDXF RRKWM RfWe[ RWRS[',
	    'PoYKaK RbPlP RbUlU Rh[k[ R^E^[ RbKb[ RgCgK RbKlKl[ R]MaQ RRKVM RbEdI RSDWF RlEjI R]KXU RUSS[ R`DYE',
	    'PoZIkI R^LhL R\\PlP R_VkV R_ZkZ R_S_[ RcJcP R_SkSk[ RZDkDkI RSDXF RRKWM RVSS[ RkKgO\\U RZDZQYY',
	    'Po`GhG R^LlL RaUjU RaZjZ RbPb[ RdCdL R`RjQj[ RSDWE RgNiO RhI_R R\\GXK R[QWU RRKVM RkDhJ RVSSZ R\\MZS RYD]K]V\\[Y[ R]D[I]Q',
	    'PoYGbG RYKbK RZRaR RZVaV R_[a[ RZNZ[ R[C[K R`C`K RZNaNa[ RUDWF RhWl[ RfWb[ RfQjY RRKVM RiPeY RVRSZ ReLhW RfDcN RdIgU RdIkIgV',
	    'PoZMjM R[QjQ RYUlU RZEZM R_W`[ RbEbT RZEjEjM RiWkY R\\XY[ RdWe[ R]G_K RTDXF RRKWM RWRS[ RfGeK',
	    'Po[GaG RdFlF RYNlN R[RiR RZUjU RXXlX RbJb[ R[KiKiR RgFhI R]F^I RTDXF RRLWM R\\CYI RWSS[ ReCcH',
	    'PoYFaF RcFlF R\\TiT R[WiW Re[i[ R]C]O R`H`L RgCgO R\\PiPi[ RjHkLlN RTDXF RRKWM RVRSZ RZHZN RdHcN R\\P[Y',
	    'Po[FkF RYRaR RcRkR R^O^[ RcDcN RgOg[ RSDWE R]RaV RiUlY RhJkM RgQjW RRKVM RaFgI RgPcY RVSSZ R^PZX RcF^KZM',
	    'PoZHkH R]MkM RaPdP ReSlS RcWlW R\\[_[ Re[h[ R_U_[ RhUh[ RZDkDkH RkShU RTDXF RRKWM RVSSZ R_JhJcMdP R\\SbSaV[W RZDZQYY',
	    'PoYHkH R^[a[ RZEbEbL RbQb[ R\\S^T RgSeT RTDXF RaNkR RRLVM RdVjY RVSSZ RaG_JYM RbNXR R_WXX RbIkL RhC_D',
	    'PoZEkE RYJlJ R[PiP R[TiT RZWjW RX[l[ R[M[T RbCbF RbMb[ R[MiMiT R^F^I RTDXF RRKWM RWSS[ RfFfI',
	    'Po[GkG RYOlO R\\ViV R\\ZiZ R\\R\\[ R`G`O ReGeO R\\RiRi[ RgCfF R\\I]M R^C_F RTDXF RRKWM RWRS[ RiIhM',
	    'PoZIkI RcQkQ R`WlW RbCbO RcQcW RhOh[ R\\D^G RZS^VZ[ RiDfG R\\QXU RdJlN RTDXF RRKWM RVSSZ R[RaQ]X RbH_LYN',
	    'Po\\KkK R^PiP R\\RiR R]WjW RZZkZ RZDZ[ R^M^P R^R]V RdPdY R^GhHhK R^MiMiP RZDlDl[ RTDXF RRKWM RWRTZ RbF_J',
	    'Po]N`N RgTlT R`[l[ RYRYZ R]P]Z RYEaEbL RcRc[ RdGdJ RgPg[ RcElElM RUDWF RRKVM RYGXJ RVSS[ RZNXO ReNbO RkNgN RaT]T R]E\\L RgEfM RaYWZ',
	    'PocHjH RYMlM R]TaT RgTkT R`[l[ RYRY[ R]F]M R]O]Z RcCcM RcRc[ RgOg[ RTDWG RRLVM RVSS[ RaYWZ',
	    'PoYJbJ RYMbM RYQbQ RYTaT RYEYJ R]C]J R]Q]X RbEbJ RhWl[ RgUb[ RfRjZ RSDWE RRKVM RiPfX RVSSZ RfDcN ReJgW ReIkIhU RbXYY',
	    'Po\\DlD R^IjI R^KjK R[[_[ R_Q_[ RcDcG R^GjGjN R_RcWjZ R[MRQ RST\\TXXRZ RiRbV RYDRG RZHSK R^G^NhNlP RaO[Q',
	    'Po^KfK R\\ThT RY[l[ R\\O\\T R\\OhOhT RjHgJ RhDeG R^V_Z RTDXF RRKWM RfVeZ RWRS[ R[E`E^IYN RdDeHkM R[G^I',
	    'Po[HlH R]KiK R\\NjN R[PlP R]VjV R]YjY RZ[l[ R]R]Y RcCcP RcRcY R]EiEiK R]RjRjY RTDWF RRKWM RWSTZ',
	    'Po[FjF RZIlI RZMaM RcMkM RcQlQ RYTlT Re[l[ R]K]Q RcCcI RfTf[ RgKgQ RlXl[ RTDXF RRKWM RVSSZ R_T]YX[ RaPYQ',
	    'Poc[h[ RZNZQ RZNlNlQ R^HaK RgHkL RkEjH RaEaH RTDXF RRKWM R[E[H RVRSZ ReEdH R\\KZL RaV^YZ[ RbQ`X R[SjSh[ R^C]J RhCgJ',
	    'PoXFcF RYLbL RZRaR RZVaV R^[a[ RZOZ[ RZOaOa[ RhWl[ RTDWF R_IbL RgWc[ RfQjY RRKVM RiPeY RWRSZ R]EZL RdKhX RfDcO ReIkIhV',
	    'PoYEbE RZNaN RZSaS RZJZX R]E]J RXJbJa[ RhWl[ RSDWE RgVc[ RfQiY RRKVM RiPeY RVSSZ RdJgV RdIkIhV ReCcN RaWXX',
	    'PoZGlG ReNjN R[PjP R[SjS RYUlU Rb[f[ R[J[R ReGeM RfSf[ R[JjJjR RTDWG RRLVM R]W_Z R^D`E R`G`L^N RhDdG RWRSZ',
	    'Po\\HiH R\\KiK RZNlN R]UiU R\\D\\K R]Q]U RcLcN RcUc[ R]QiQiU R\\DjDjK RhWk[ R^WZZ RTDXF RRKWM RWRS[ R_[c[_[',
	    'PoYGcG R[KaK ReLlL R[PaP RXTcT R[C[T RaCaT RiLi[ R`VaY RTDWF RRKVM RVSSZ R[VY[ RjCeFeScZ',
	    'PoYGcG RdJlJ RYKcK RZSbS Rf[j[ RZNZS R[T[X R^C^K RZNbNbS RjCj[ ReNgT RSDWF RRKVM RVSSZ R`T`X RcYXZ',
	    'PoYFcF RYJbJ RZRbR RZMZR R^C^J RZMbMbR RkDeJ RkLfQ R[T[X RSDWE RRKVM RVSSZ RlTgYc[ R`T`X RcXXZ',
	    'Po^IiI RZRbR ReRlR R[[l[ RZLZR R[U[[ R^E^I RZLbLbR RcTcZ ReLeR R^EiEiI RlUl[ ReLlLlR RUDXG RRLVN RWSS[',
	    'Po[DjD R\\K`K ReKiK R[N`N ReNiN R[QjQ RXTlT R\\ZjZ RZZeZ RZHYK RbDbN RYHkHlK RfVk[ RTDXF RRKWM RVSSZ R_T]Z',
	    'PoYDlD RZI`I RZN`N ReNkN RZVjV RZZjZ RZIZN RZRZ[ R`D`R RbRbZ ReDeR RZRjRj[ ReIkIkN RTDXF RRKWM RVSSZ',
	    'Po_ElE RXG_G RaMjM RaQjQ RaVjV R\\G\\U RaIaV ReEdI RaIjIjV RhWl[ RSDWF RRKVM RVSSZ RbX\\[ R_TWV',
	    'PoaGlG RaLjL RaPjP R\\K\\[ RaIaP RaIjIjP RTDWG RcQ^V R]DXI R]JWO RRKVM RbTfYl[ RVSSZ RbC_J RbSjSfX_[',
	    'PobHlH R`MlM RYO^O RYW^W RaZkZ RYHYX R[CZH^H_W RaRa[ RfCfQ RaRkRk[ RSDVF RRKVM RUSS[ RbD`K',
	    'PoZFlF R[JkJ RZNlN R[SkS RYWlW R^J^S R_C_H RcJc[ RgCgH RiJiS RTDXF RRKVM RWRSZ',
	    'PoZHaH ReHlH RZLaL ReLlL R]QiQ Rh[l[ RZDZ[ RZDaDaL RcNcZ ReDeL ReDlDl[ RdSiX RcOaS\\X RcOdT RTDXF RRKWM RWRSZ',
	    'PoZHaH ReHlH RZLaL ReLlL R_TgT R_WgW Rg[l[ RZDZ[ R_P_Y RZDaDaL ReDeL R_PgPgW ReDlDl[ RTDXF RRKWM RWRSZ',
	    'PoZGaG RcGlG R\\UiU R\\ZiZ R\\Q\\[ R^C^O RgCgO R\\QiQi[ R^HaL RhJlM RfJbM R\\JYM RgFjL RSDXF RRKWM RWRS[ R^F[L RgFeK',
	    'Po[IaI RdIkI R[MkM R\\QlQ Rf[k[ R_V_Y RdEkEkL RfUgX RcUcY RTDXF RRKWM RWRS[ RZUYZ RaD[F\\L[SlTk[',
	    'Po]KhK RXRlR Rc[g[ R[F[P RcHcP R[FjFjP RbK^O RbD`F RTDXF R`KgN RRKWM RVSSZ R^R\\ViUh[ RfG^H',
	    'Po[KkK R[OkO RZQlQ R\\WiW R\\ZiZ R\\G\\N R\\S\\[ RdHdN R\\SjSj[ RZIkHkN R_CYI R]FfEcH RTDXF RRKVM RWRSZ',
	    'Po]JiJ R]ShS Rh[l[ RYIY[ R]M]S R`O`R RcHcY R]MhMhS R`ElEl[ ReTiX RcRgV RTDWG RRLVN RZD]G RcR^X RfOeR RVSSZ',
	    'Po_MgM R[SkS R[VkV Rg[k[ RZFZI R[O[[ R`H`L RcCcF RcOc[ R[OkOk[ RZFlFlI RaGdI RhIlL R\\JYL RTDWF RRKWM RWRS[',
	    'PobEjE R\\KgK RaMiM R^RiR R]TjT R[WlW R^N^R RaHaM RbCbH R^NiNiR RZHlHkK RTDWE RRKWM RWSSZ RcTbWaY[[ ReYl[ RZHZQYX',
	    'PoYFlF R^MfM R[PjP RXUlU R[H[P R^K^N RbCbF RZSbRc[ R^KfKfN R[HjHjP RSDWE RRKVM RcUfXlZ RWRS[ RbT`WXZ RhQ`R',
	    'Po[I`I RdIjI RZM`M RZQ`Q RdQkQ RZV`V RdWkW Ra[k[ RZMZX R[E[I RaEa[ RdEdX R[EjEjI RdMkMkV RkXl[ RTDXF RRKWM RVSS[',
	    'Po]IhI RZPaP RdPkP RYTlT RZLZP R]D]I RZLaLaP RbQb[ RdLdP R]DhDhI RdLkLkP RTDXF RRLVM RbSgXlZ RVRSZ RbS^XXZ',
	    'Po]OiO R[VjV R[ZjZ RZHZK R[R[[ R]K]N RcCcH RcRcZ R]KiKiN R[RjRj[ RZHlHlK R]D^F RiDhF RTDXF RRKWM RWRS[',
	    'PoXGbG RcMkM RYNaN RYRaR RbSlS RXVbV RYJYR R]C]J R]R][ RYJaJaR RgMg[ RUDWF RgCkK RRKVM RVSSZ RfCcL',
	    'PoZIkI RZLjL RXPlP RYTlT RZWkW RZDZI R`D`I RbJbO RbTb[ ReDeI RZDkDkI RgPfS R^Q^T RTDXF RRKWM RVSSZ',
	    'PoZOaO RdQkQ RYTaT RcUlU R^[a[ R^J^T RYKaKa[ RhMh[ RSDVF RRKVM RVSRZ R[F_F]L RfEeJ ReNcT RjKhK R\\CYJYTXZ RcEkEjK',
	    'PoaEkE RbMjM RbQjQ RbVjV RY[[[ R\\M\\[ RbIbU RbIjIjU RhWl[ RTDWF RYH]L RdW`[ RXE_E]I RRKVM RVSSZ RXM`M^R ReEeI',
	    'Po[IjI R[LjL RZOlO R\\ViV RZ[l[ R[E[L R`C`L ReCeL R\\RiRiV R[EjEjL R_W_Z RTDXF RRKWM RfWfZ RWSS[ R\\R\\V',
	    'Po[GcG RdGlG RZMkM RY[l[ RbMb[ R]RaV RgSlW R]F_J RgFhJ RTDXF RRKWM RhOeW R\\DYK RWSS[ R^OZW RfDbK',
	    'PoTGZG RdGjG RUJZJ RdJiJ RZ[_[ RRMRQ RUEUM R_P_[ RdDjDjL R\\IbK R_RcWkZ R\\DbG R`J[L RSS\\SWXRZ RgSbU R`E[G RRMlMlQ RYDTD',
	    'Po[DjD R\\J`J RdJiJ R[M`M RdMiM R^RfR ReZiZ RYGYK RaUa[ RbDbL R[UiUiY RYGkGkK RSDXF RbNlS RRKWM RVSSZ RbN_PXS',
	    'PodFlF RZFbF R]MjM R]PjP R^WhW Rg[l[ R[R[[ R]J]P R`C`H RcJcW RgCgH R]JjJjP RZSlSl[ RgUhY RTDXF RRKWM RWRS[',
	    'Po[QiQ R[TiT R[XiX R[N[X R[NiNiX R`FkFiH RaD^G RTDXF RRKWM RdHfJkL RZD]E RWRSZ R]IYK RdHbK]L RfYl[ R_YY[',
	    'PoZGlG RbMlM RbPkP RbUkU RbZlZ RcKc[ RgLgZ R^VY[ RbCcG RgIhL RTDXF RRKVM RWSS[ R\\IYO R^L[S RcI`P R_R\\Y R_SYU R^NYN',
	    'Po[QiQ R\\WhW R\\ZhZ R[K[Q R\\T\\[ RbKbQ R\\ThTh[ R[KiKiQ R^M^O RbCfG RfMdO RSDXF RRKWM R^IeHlJ RVSSZ RaC]HXJ',
	    'Po\\IbI RYTlT R_C_H RbOb[ R\\Q^S RdIgJkN RjHdM RhQeS R\\HYK RaJ\\O RTDXF RRKWM RbSeWlZ ReEkEhJ RVSSZ R[J^MYO RbS^WXZ RbE_EcE',
	    'PoZR`R RdRjR RZMZR RZM`M`R RdMdR RdMjMjR RhXk[ RfWiY R]WaY RbChI R]WY[ RfWb[ RSDXF RRKVM R]JgIlJ RfTdZ RVSSZ R]T[Y RaC\\IXK',
	    'PoYIaI RZNaN RZTbT R\\K\\T R`K`T RYDaDaI RdDjDjLlLmK RSDVF R_VbX RRKVM RVSRZ R[VY[ RcOkOhVdYa[ ReEdL RYDYRWZ RdPgWl[',
	    'PoYIlI RZMkM R[QkQ RYTlT R`C`H RbMb[ RdCdH R[D\\G RjDhG RgJfL R^J^M RTDXF RRKWM RcTgXlZ RWRS[ RaT^XYZ',
	    'Po]MhM RXUlU RZFZS RcHcR RZFjFjS RbM]R ReJeL RaCbF RTDXF RaNgP RRKWM RbUeYk[ RVSSZ RbU_YZ[ RaSaW RfH]I',
	    'Po[IjI RZKlK R\\SiS R[D[I R\\N\\S R_U_[ R`D`I ReDeI R\\NiNiS R[DjDjI RRLVM RTDWF RcTgYl[ RVRSZ RcSYW RjUeW RdY]Z',
	    'PoYFbF RXMbM RcMlM RXQbQ RZH[L R]C]G R]M][ R`H_L RiMi[ RTDWF R]RaV RRKVM R[UXX RWSSZ R]PZW RjDcFcRbZ',
	    'PoZFlF R_MfM R\\PjP R]UiU R]XiX RY[l[ R\\H\\O R]R]X R_J_M RcCcF R_JfJfM R]RiRiX R\\HjHjO RTDXF RRKWM RXRT[',
	    'Po\\GiG R\\JiJ RZPjP RYSlS Rb[g[ R\\D\\J RbPbS R\\DiDiJ RZLjLjP RTDXF RRKWM RWRSZ R_S^W\\YX[ R_L_P ReLeP R_WhVg[ RZLZP',
	    'Po[GkG R]JcJ R^QcQ R^M^Q R^McMcQ R^T^[gZg[ RaTdW RiVkZ RgDiF RhOdS RTDXF RRLVM RiIfR RVSS[ RZVY[ ReDgP R[G[MZS RlQlTiS',
	    'Po[IlI ReLjL R_NiN R[QlQ R^SiS R^UiU R^ZjZ R^W^[ RfIfL R^WjWj[ R^EXK R]EeEcH RTDXF RRKWM RVSSZ R`K\\M R[H[RYY',
	    'PoZFkF RYKlK R\\QiQ R\\TiT R\\M\\T R\\MiMiT RhWlZ RaCbF RfHeK R^H^J RTDXF RRKVM R`UeW R^V^[gZg[ RWSS[ RZWXZ',
	    'Po^HkH R_[l[ RgOgX RSDVF RRLVM RZE[I ReNkT RXM[M[XX[ RaCbG RVSR[ RiCfH RdH`M^M ReMbP^R RjKfPbU^W RfYbY ReMfV R[X_Ze[',
	    'PobGlG RZL`L RbNlN RbTlT R[YaY RZEZR[[ RZE`E`L R[QaQaY RdIeM RgCgG RgNg[ RjIiM RSDWE RRKVM RVSSZ RZNXY',
	    'PobEjE RbNjN R]PkP RbCbM RhVlZ RTDXF RRKVM R[HjHjK RWRSZ ReP]S RdS\\V RjReT RdW\\Z RcReVeY Re[a[ R[H[RZY RgJbK^K',
	    'Po_FjF R^JlJ R^OkO R_RjR R^VlV R_[l[ RdCdJ RdOdY RTDWG RYK\\N RZE\\G RXQ[Q[XX[ RaKaO RRLVM RhKgN RVSS[',
	    'Po[GbG RdGlG R\\PjP RZSlS R_C_N RcSc[ RhChN RhVkZ R^GbJ RjIkM RgGkJ RfIcM R]VYZ RTDXF RRLVM RWRSZ R_E\\L RgEeK',
	    'PoZJaJ RZNbN RXQcQ R\\[`[ RZFZN RZFaFbN RSDWE RRKVM R]C]F RVSSZ ReCcN R[TaT`Z R]O[UYZ RdJfRhWl[ RdIlI RjIhTfWc[',
	    'Po[IlI R\\UgU R[YhY R[D[I R\\P\\T R`D`I RaNaX RfDfI R\\PgPgT R[DlDlI ReVhY RTDXF RRKWM RWSS[ R^IYP R\\MmMmUk\\g\\',
	    'PoYFlF RYLlL RYQlQ R\\TiT RaIa[ RbCbF ReIe[ R\\NiNiT ReTkZ RSDWE R`TY[ RRKVM RVSSZ RcH\\J RiHcJ RYFYSXY',
	    'Po[HjH R[LjL RZNlN R]QiQ RZTlT R[E[L R^T^[ R`C`L ReCeL R[EjEjL RTDXF RRKWM RcUgYl[ RVSSZ RjVfW ReY\\[ RZNZTYY',
	    'PobUgU R`[l[ R_M_X RbPbV R`HeIeM RbPgPgU R`DiDiM RUDWG RXM\\M[W^Z RYE\\H R[WX[ RRLVM RWRS[ R`D_MkMjXhX',
	    'PoYJlJ R\\LhL RZViV RYEYH R\\Q\\T RbObU R\\HhHhL RYOYPkOkQ RYEkEkH RSDWE RRKVM RVSSZ R^Xh[ R\\QiQiTfT RhV`Yk[ ReYZ[',
	    'Po[FkF RZJlJ R\\PjP R\\TjT R\\WjW R\\M\\W R_H_L RcCcH RgHgL R\\MjMjW RTDXF RRKWM RfXl[ RWRS[ R_YX[',
	    'PoZFkF RXNlN R_XfX R[ZjZ R[Q[[ R_T_W RbCbN R_TfTeX R[QjQj[ R]I`L RgIjM RTDXF RRKWM RWRS[ R]H[L RgHeL',
	    'PoZIaI RZPaP RZSaS RZDZI RZLZX RXLbLa[ RZDaDaI RhEkH RgVc[ RSDWE RRKVM RjOfX RVSSZ RlKcM RfCfLhY RlWj[k[ R_WWX',
	    'PoaIlI RXK^K RbNkN RXQ^Q RbTkT RXX^X RbZlZ RXEXZ RXE^E^X RbGb[ RgGgZ RSDVF RRKVM RVSRZ RcC_M RhDgI RcDbL',
	    'PocFiF RYIlI RZMlM R\\PcP R]D]I RlXk[ RgJiK R_P`V\\Z RgUa[ RTDXF RRKWM RiOeW RWRSZ R\\R[V RcCcIfV RZMZSXY',
	    'PoYFaF RcFlF RbUkU Ra[l[ R^C^N RcPcZ RgCgN R\\W`Z R]FaK RfHbL R^FYL RjIkL RSDWE RRKWM RYPjPjS RVSS[ RgEeK R\\SZZ',
	    'PoRH]H RSO]O RbOeO RSR]R RSU]U R`UkU R`ZkZ RSKSR RSK]K]R R`Q`[ ReCeO R`QkQk[ RgIlM RjFgI ReDhK R_GcG`L RXEXYRZ R]YYYUY R\\DSE',
	    'PoZFlF R_C_H RfCfH R^U^[gZf[ R\\KjJjR RbUcW RdO`S R]JYN R`M[R RiUkZ RTDWF RRLVM RWRS[ RZUYZ RfJbQ RiSgS R`J]OfP',
	    'Po^GhG R^JhJ R^NhN ReQlQ RXT`T R^D^N RZFZQ`Q`[ ReQe[ R^DhDhN RlFlP ReTlTl[ RSDVG RRLUM RVSRZ R\\TYZ',
	    'PoYEaE RcEkE RZIaI RdIkI RcNlN R_[g[ R]E]N R_R_[ RgEgN R`OcR RSDWE RgXg[ RiSlY RRLVM RWSS[ R[TYY RhQbWY[ RaMYN',
	    'Po[GjG R[JjJ R[MfM RYPlP R]WjW R]ZjZ R[D[J R]S][ RaJaP R]TjSj[ R[DjDjJ RSDXF RRKWM RVSSZ RjLgO_SXT',
	    'PoYGaG RYQaQ RYKYQ R]C][ RYKaKaQ RdJdU RdJkJkU RhWl[ RTDVG R\\QaV RRLUN RfWc[ ReFjFhJ RVSSZ R]PZW RfCcJ RhMhRfX',
	    'PocElE R^GaG RXLbL RZFZL R^C^T RcIcU RcIkIkU RhWl[ RTDWG RRLUN R_UY[ RfWb[ RVSRZ RaP]X RgEfI RZOXU RgLgQfX',
	    'PoYFkF RYJlJ R[QlQ R\\TjT R`J`[ RfJf[ R\\NjNjT ReTkZ RSDVG RRLUM R^G`L RaT[Z RbCcF RVSRZ RYJYTXZ',
	    'PoYDlD Rg[j[ RZJZ[ RbDb[ RZJkJkZ ReThW RSDWE R\\N_Q R\\T`W RfNhQ R_T[X RhMdQ RhTdX R_M[Q RRKVM RfGlI RVSSZ R^GXI',
	    'Po[HiH R[KiK RcUkU RYUaU R[D[K R`W`[ R[DiDiK R_SbU RZO]R RcOgR RjSlU R_OZU RjOdU R]LZO RgLdO RdWe[ RjWk[ RTDXF RRKWM RVSSZ R[WY[',
	    'Po[IjI R[LfL RYPlP R]ViV R]ZiZ R[D[I R]S][ R`D`IaP ReDeI R]SjSj[ R[DjDjI RTDXF RRKWM RWRS[ RiKeOYU',
	    'PoYFlF R[LiL R[OiO RXRlR ReZhZ R[I[O R\\T\\Z R_C_G RbSb[ ReCeG R[UiUiZ R[IiIiO RTDVF RfRhTlV RRKVM RVSRZ RaO\\TXV',
	    'Po_LgL RYSlS RYUlU R^[b[ RZFZH R[N[S R_H_L R`N`S RbCbF RbUb[ ReNeS R[NjNjS RZFkFkH RhIkL R\\I[L RTDXF RRKWM R`GdI RVSSZ',
	    'PoYMYP RYMkMkP RTDWF R\\HaJ R\\Q[U RkEiG RiQfT RaE`G RRLVM RbRcUfYl[ RfHkJ RZEZH RVSSZ RdEcH R[JYK ReJcK RaU]YX[ R]C\\J RbO`W RgCeJ',
	    'PoZSkS RZNZQ RbPb[ RZNkNlQ R]IaJ RhIkL RjEiH RaF`H RTDXF RRKWM RbRhXlZ R[FZI RWRS[ RcFcH R\\KYL ReKbL RbR\\YYZ RgDeK R^D]J',
	    'PoYFlF R]LhL RZPkP RZIZL R_C_H RfCfH RZIkIkL ReTjY RRKWM RcPfW RTDXF RVSSZ RjSfU RdWd[_[ RbQYS RaTYV R`QcTdW^YYZ',
	    'Po[HlH R\\LiL RZOlO RX[l[ R[U[[ R`U`[ R`P`S RcCcN ReUe[ R\\EiEiK R[UjUj[ RhPlS R\\P[T RdPeS RTDXF RRKVM RWRS[',
	    'PoZFkF R\\HgH R\\KgK RYNkN RZUiU R^[b[ R\\C\\M RbUb[ RgCgM RTDWG RfSiV RfOlQ RRLUM RfXkZ RVSSZ R]OXQ RaP\\RdR[U R]XXZ',
	    'Po\\GiG R\\JiJ R\\LiL R[QkQ R[SkS RYVlV R[N[S R\\E\\L RcNcS RfVf[ R\\EiEiL R[NkNkS RTDXF RRKWM RWRSZ RbCbE R_V^YY[',
	    'PoYFlF R\\WiW RbCbF RbJbO RfJfN R\\RiQi[ R`HaI ReHcI RiIkO RTDXF RRKWM RVSSZ R\\IZO RkIfJ RZI_J^O]N RiNdN R\\P\\U[Y',
	    'PoZFkF R\\KhK R\\PiP RZMZP R\\H\\K RbCbF R\\HhHhK RZMkMkP RSDXF RRKWM RVSS[ RcP[S RaT[W R`RdWd[a[ RdQfUiXlZ RdWZZ RjSfT',
	    'PoZDjD R\\J`J RdJiJ R[M`M RdMjM RYPlP Rh[j[ RYGYJ R[S[[ R`S`[ RbPaS RbDbN ReSe[ R[SkSk[ RYGkGkJ RTDXF RRKVM RVRSZ',
	    'PoZOkO RYWlW RZDaDaL RcUc[ RcDkDkL R^R`T RfRjT R[G^H RdFhH R^Q[T RgQdT RTDXF RRKWM RWRS[ RaIZL RkHdK',
	    'PoXG`G RYN_N RYR_R RWV`V RYJYR R\\C\\J R\\R\\[ RYJ_J_R RhJh[ RSDVG RSLUN RbOeR RcLeN RgElJ RfCdGaJ RVSRZ RkTbU',
	    'PoYElE R[HiH R\\MhM RZOjO RXRlR RaUlU RZY`Y Re[h[ RZTZZ RZT`T`Y RbCbH RbMbO RhSh[ RYKkKkM RcWeY RTDXF RRKWM RVSSZ',
	    'Po_EkE R\\NjN R[TjT R[WjW RZLZO R[Q[W RcNcW R[QjQjW RZLlLlO RTDXF RRKVM RfYl[ RWRSZ RbC[F R`YY[ RiFbHZJ',
	    'Po[IjI RZLlL R[QjQ R[TjT RbIcL R[OjOjT RaUdX RhWlZ R\\F^H RiFfI R[EcEbH RTDXF RRKVM R^V^[gZg[ RWRS[ R[WY[ RjCbD',
	    'PodIlI RYJcJ RYOcO RYTbT Rf[j[ R[K[N R\\C\\J R^O^Y R_C_J RjDj[ RSDVF RYEYH RRKVM RVSRZ RbEbH RaK_O RfOgU RbXXZ',
	    'PoYFlF RcSlS RYSbS R_C_H RfCfH R_V_[gZg[ R_PbR RYL]O RhVlZ RcLfO RiOlR RgIdL RiMdR R]IZL R_MZR RTDWF RRKWM R`UdV RWSS[ R[VYZ',
	    'PoZElE R[KjK R\\MiM R\\OiO R\\RkR R\\I\\R R_C_G RcIcR RfCfG R\\IYM RTDXF RRKWM RWRS[ R]T_WcYk[ RZTiTgWcY^ZY[',
	    'PoXE`E RXH_H RWL`L RbRkR RXS_S RXV_V Rc[k[ RXOX[ R\\C\\L RcKcZ RgJgR RbKkKkR RSDVF RRKUN RlVl[ RUSRZ ReCaK RdFiFgL RXO_O_[\\[',
	    'PoYDbD ReHlH RYLbL ReNlN RYPbP RX[l[ RYDYP R[S[[ R^L^P R`S`[ RYHbHbL ReSe[ R[SjSj[ RUDWG RRLVM RVSS[ R^D^H ReCdK',
	    'PobEjE R]JhJ R]UiU R]XiX R]ZiZ RZHZK R]R][ RbCbH R[SiRi[ RZHkHkK RhMjP R]LZP RTDXF RcMfPgRlT RRKWM RVSSZ RbM]RXT',
	    'PoZFlF R\\JjJ RZMlM R`RjR R\\RgR RZ[l[ R\\T\\Z R`C`H RaTaZ RcHcM ReTeZ RgCgH R\\TjTjZ RgOkR RaM\\R RTDXF RRKWM RWSTZ',
	    'Po]IlI R^NkN R_QjQ R]UlU R[H[[ RbCbI RfCfI RRLVM RaJaM R^EaG RjEhG RgJfN RTDXF RdNdTeVhYl[ RWRSZ RcV`Y][ R\\DYM RdPcW',
	    'Po[HaH ReHkH RZKaK RdKkK R[QkQ R\\SjS R\\WjW R\\ZlZ R\\O\\[ RZDaDaK RdOdZ RdDkDkK R\\OXS RTDXF RRKWM RVSSZ',
	    'PobJlJ RbNkN RXP`P RbTkT RbZlZ R\\O\\[ RcHc[ RgHgZ RYH\\K RSDVE R[MYO R_R`W R^L`P RRKVM R\\CZH RVRSZ R^G[M RYRXY RhDgJ RdCaIbM',
	    'Po]HiH R\\QjQ R\\TjT R\\WjW RZFZI R]N]W RbCbF RbHbM RYOjOjW RZFlFkI RTDXF RRKWM RfYl[ RVSSZ R_KZM R`YY[ RhK`MXO',
	    'PoXFbF RYIaI RjLlL RYSbS RXLXO R]C]I R^O^[ RXLbLbO ReDeK ReDjDjL RgVl[ RSDVF R]TaX RRLUN RiUc[ R]TXY RePiY RdOkOgX RUSS[ R`NZO',
	    'PoZGaG RdGkG RYJaJ RcJlJ R[VjV R[ZjZ R[R[[ R]D^J_O R[RjRj[ RiJiOlNlO R[MXP RTDXF RRKWM R[DYH RVSSZ R\\JZO RdDcH RgDfJcO RbN]N',
	    'PoZS`S R_[b[ RZMZQ RbOb[ RZMlMlQ RdSjY RTDWE RbOeU R^HaK RgIkJ RkEjH RbE`G RiReT RRKVM R[EZH RVSSZ RdEdH R\\KYL RfKcL R_U[YXZ R^C]J RhCgJ',
	    'Po\\LjL R^RjR R^TjT R^WjW RYFYI R^O^W RcCcF RcIcL R\\IjIjL R^OjOjW RYFlFlI RTDXF RRKWM RgYl[ RWSSZ RaY\\[ R\\I\\PZW',
	    'PoZHaH ReHlH RZKaK ReKlK R]QiQ R_YgY Rg[l[ RZDZ[ R_T_Y RZDaDaK RcNcT ReDeK R_TgTgY ReDlDl[ RTDXF RRKWM RWRSZ RfM_N',
	    'PoTF\\F RYNlN R^QgQ Rg[k[ RZSZ[ R^L^P RbQb[ RgLgP RZSkSk[ RWVRZ R^V[Y RfUhWdY R^U`Y RRRVS R_DbGjK RhFcH R[GSK RSMWN R_C_K\\K',
	    'PoYFlF R[QhQ R[ThT R]ZiZ R[N\\V][ R^C^H RfCfH R[NhNhS R]ViVi[ RSDVG RSLUN RaKcM RbHjL RVSRZ R\\SX[ RbHYL R[PZX',
	    'Po[KjK R[MjM RYPlP R[WjW RX[l[ R[D[K RbUb[ RbDbP R[DjDjK RdReT RhRkU R\\RYU R`R`U R^F_I RTDXF RRKWM RWSS[ RfFfI',
	    'PodGlG RYJ_J RYO_O RdSlS RaShS RYT_T RYDYT RYD_D_T RlXl[ RSDWE R^V_Y ReJiN RfOiP RRKVM RjTfXh[k[ RgDkD RVSSZ RZVXZ RaHiHgKaN RhW`[ RcDfL RkKkNiM RdOeX',
	    'Po`IkI R_KlK R`QjQ R`TjT R`WjW RYDY[ R`F`I R`N`W ReCeK R`NjNjW R`FkFkI RSDVF RRLUM RgYlZ RVSRZ RcY^Z R\\M^T R^V[V RYD^D\\M',
	    'Po_KdK RePkP R_PdP RXSkS R_F_P RbPb[ RaC`FeFeP RYH\\K RfHiK RjLlP RSDWE RRKVM RcSfWlZ RjGfO R[CYH RVRRZ R]HYP RhCgH RbS]XXZ R]L^PXP',
	    'PoZFaF RdFkF RZIaI RcIlI R\\QiQ R\\TiT R\\WiW R\\N\\W R\\NiNiW R]I`L RgIkM RhIdM R]JZM RTDWF RRKWM ReXl[ RWRS[ R_XY[ R^D]K RgDgK',
	    'PoaEjE R^KgK R]RiR R]UiU R`[g[ R]O]U RaWa[ RcOcU R]OiOiU RZHkHkK RiWlZ R]W[Z RSDXF RRKWM RbVeW RVSS[ RZHZRYZ RaDaH R_J_MgM',
	    'Po[ElE ReHlH R]HcH R^NjN R^QjQ Rh[k[ R]S][ R^K^Q RaFaJ RdKdX RhFhJ R^KjKjQ R\\TkTk[ R_XhWiY RTDXF RRKVM RWRSZ R[E[RYY',
	    'PoYDlD R\\IhI R\\LhL R_SfS R]VgV RZNYQ R\\G\\N R_P_T RbDaG R\\GhGiN RZNkNkQ RhPjS R\\QZS RTDXF R_NdP RRKWM RWRSZ R^UYW R^WcYl[ RfWaYY[',
	    'PoZFkF R[IjI RYLlL R_NeN R]SgS R]UgU R]O]Z RbFbL R\\PgPgU ReLkR R]D_E RgDeE R_MYS RSDXF RRKWM RbUdXk[ RVSSZ RjWdX RcZZ[',
	    'Po\\KjK R]WjW R]ZjZ R]S][ RcGcN R]SjSj[ R[HjHjN RePeR R]G\\NiOlR R`CZI R^EeEcI R\\PZR R`P`R RTDXF RRKWM RWRS[',
	    'PobIkI RYJ_J RbMkM RYO_O RYT_T Rg[k[ RYDYT RYD_D_T RbDkDkM R^V_Z ReSaW RTDXF RRKWM RiQeY RVRSZ RZVX[ RbDbMbRlQlVk[',
	    'Po[GbG RdGlG R[ObO R[SbS RgWkW R[L[Z R[LbLbS RdLd[ RdLkLkW R^F_I RfFiI RSDXF RRKVM R\\CZI RWRS[ ReCcI R`UbYYY',
	    'Po[QiQ R[ViV RY[l[ RYNYQ RbQb[ RYNlNlQ RUDWG ReWhY RRLVM R^HaK RhIlL RkEjH R[EZH RaE`H RdEcH RWST[ R\\KYL RfKbL R^C]J RgCfJ',
	    'PoZElE R[HkH ReMkM R[NkN R\\SjS R\\UjU R\\XjX R[J[N R\\Q\\X RcCcH ReJeM R\\QjQjX R[JkJkN RTDXF RRKWM RfYl[ RWRS[ RaK]M R`YX[',
	    'PoZElE RZIlI R_LgL RZNlN R]TjT R]WjW R]Q]W R_E_L RcCcF RcNcW RgEgL R]QjQjW RTDXF RRKWM RgYl[ RWSSZ R`Y[[ RZEZPYX',
	    'Po[OiO R[SiS R[ViV RXXlX RYLYO R[Q[V RbLb[ R[QiQiV RYLkLkO RTDWF R[F^G RRKUM RdFgG RVSSZ RYDaDaIYJ RcDjDjIcJ',
	    'Po\\MaM ReMjM R\\PjP Rf[j[ RZFZI R\\J\\P R`W`Z RcCcF ReJjJjP RZFlFlI RfUiX RcVdY R`PYW RTDYF RRKWM RWRTZ R\\W[Z RbH\\J R]TlTk[',
	    'PoX[l[ RZTZ[ R]M]P R_T_[ RZF`F`R RdTd[ RZTjTj[ RTDWF R]G]J RdMgQlS RRKVM R]C]F RVTS[ RlGlJ RcLjLfQbR RdEiEkIiI RdEdJ RZFZLYR R`KYL',
	    'PoYNlN R\\WiW R\\ZiZ RZFZI R\\T\\[ RbCbF RcIcR RbTbZ R\\TiTi[ RZFkFkI R]K_M RgJfN RTDXF RdNfQlS RRKWM RVSSZ RaN_QXS RhH^I',
	    'PoZFlF RZMkM R]PcP ReTlT R]N]U R`F`M RaWa[ RfNfT ReFeM RZIkIkM RjWk[ RbCcF ReWfZ RTDXF RRKVM RWRSZ R\\WZ[ RkOeQ RcS\\T RZFZQYZ',
	    'PoZFlF R]KjK R]MjM R]PjP R[RlR ReUkU R]XjX R[[l[ R]H]M RcCcF RcUc[ RcHdReU R]HjHjM RSDWE RRKVM RVSS[ RbR_U\\V RZFZQXZ',
	    'PoeFlF R[NjN R[RlR R[I[N RZF`E`H ReCeH R[IjIjN RlXl[ R\\T_W RTDXF ReOhP RRKVM RhTdXgZj[ RWRSZ ReX_[ R`I_N RbOdX ReIeN RZRZY',
	    'PoaLdL R[QbQ RYTdT R^ZdZ RX[^[ Rh[l[ RYEYK R_EeEeK RgGgU RlCl[ RTDVF RRKVM R^MaNdP R^MYQ R[V[Y RbVaX RVSS[ R]G^K\\L R]DYE RaE^JWK R^Q`Z\\Z',
	    'PoZFcF RYJlJ R`NdN RYQ]Q RYT]T R`TdT R]J]X R^C_J`X RSDWE RiEjH RgUb[ RRKVM RjOfX RVSSZ RYN]NYN R`QcQ`Q RfCfNfPhY RdWXY RlWk[i[',
	    'Po[GiG R[JiJ RZMjM RXQlQ R^[b[ R[D[J RbSb[ R[DiDiJ R]T_V RRKWM RfTdV RTDXF RfKfQiTlU RdXjZ RVSSZ R^K^Q[TWV R_XYZ',
	    'Po[FkF R\\OiO R_XgX R[ZjZ R[R[[ R_T_X RcCcN R_TgTgX R[RjRj[ R]H_J RhHjJ RhHeJ R]HZK RTDXF RRLWM RWRSZ RbKlM RcKYN',
	    'Po]JdJ R]NdN R\\QdQ R]UdU RZKZ[ R]E]J R`Q`Y R`C`J RdEdJ RSDVF RSKUN RZEWH RgRk[ RiReZ R[IWQ RVSRZ RfJiX RgCdN RdX\\Y RfIkIiV',
	    'PoXFbF RYIaI RjLlL RXSbS RXLXN R]C]I R^P^[ RXLbLbN ReDeK ReDjDjL RgVl[ RSDVF R]TaX RRLUN R\\UXY RiUc[ RePhY RdOkOgX RUSS[ R]R[X RZN`OZQ',
	    'PobDlD R\\G`G RWLaL RcMjM RcQjQ RZU\\U RcVjV RYFYL R\\C\\T RcIcU RgEfI RcIjIkU RhWl[ RSDVF RRKVM R_UYZ RdWa[ RUSSZ RYOXS R`P]X',
	    'Po]DkD R]JbJ RfJjJ RfMkM R]MbM R\\RlR R\\TkT R\\WkW R\\ZlZ R[G[J R]P][ RdDdM RdQdZ R[GlGlJ R^OZS RTDXF RRKVM RWRSZ',
	    'Po[LbL R^NdN RZTjT R^[g[ RZPZT R_V_[ RbCbF R\\IbHbO R[N^OjPjT RYFYGkFkH RhWlZ R[WXZ RTDWF RRKVM R`UdW RVSS[ R_P_T RePeT RhH_I RiJ_K RjMaN',
	    'PoaEiE R]KfK RaMiM R]RiR R]TiT RZ[l[ R]V][ R]O]T RaVa[ RaCaH RbIbM RcOcT ReVe[ R]OiOiT R]VjVj[ RZHkHkK RSDWE RRLVM RWSS[ RZHZRYZ',
	    'PoYGaG RcIjI RXMbM RcNjN RYQaQ RcDjDkR RcDcRjRh[lZl[ RRKVM R`TaW R[H[L R]D]F RSDVE RVRRZ RZTXX RcZa[ ReRdZ R`H]N]ZZZ',
	    'PoXG`G RXN_N RXR_R RWV`V Ra[e[ Rg[k[ RXJXR R[C[J R[R[[ RXJ_J_R RaLeLe[ RgLkLk[ RSDVF RRKVM RgElJ RdN`R RcSaW RiSgW RdF`J RkMfR RUSS[ ReCcH',
	    'PoYEkE RYHjH RZNhN Ri[l[ RZ[\\[ RYEYH RZJZN RYP\\P][ RZJhJhN RePjPkYl[ RSDVF R_VbY RfThV RRKVM RVSRZ R`Z^[ RYS]S RaO_X R^RcRaY R\\VXW RYPYVX[ RePeVdZ',
	    'PoXEcE RYLbL ReQlQ RZUaU RXOX[ RYHYL R]U]Z RYHbHbL RXOcOc[ ReHeR RhChY ReHlHlQ RSDVF RSKUN RVSRZ R[O[S R_O_ScS R_[c[_[ RkUlYdY',
	    'PoZDlD R[JbJ RdJlJ RcUjU RZ[l[ R^T^Z R`G`Q RcRcZ RhGhQ RSDWE R^JbN RhIjO RRKVM RVSS[ R_I\\O RhIeO RbG[G RkGdG RZDYRXZ',
	    'PoXF`F RXI`I RWMaM RaPlP RbTjT RYU_U Rc[e[ RYOY[ R\\C\\L R\\O\\U RYO_O_[ RfKf[ RfFfJ RbKjKjT RSDVF RjFlJ RRKVM RUSSZ RcEaJ R][_[][ RjDcE',
	    'PoYElE RXKlK R[NiN ReSjS RZUhVjW R^C^G RZS^Q^S RZPZX_W_[ RbGb[ RePeS ReWe[ RfCfG R[IiIiN RjPj[ RSDWE RRKVM RVSSZ RZSX[',
	    'PoYUkU RZMZP RbUb[ RZMkMkP RfRiSlV R^H`J RhHjK RbM^Q RjEiH R]WZZ RcQ`U RfOaS RaE`G RTDXF RRKWM R\\PbS RfWjYlZ R[EZH RWRS[ RdEcH R\\JZK ReJcK RgDfJ R^C]J',
	    'PoaFkF RcIkI RcLlL RcRkR RcTkT Rc[l[ RXDX[ RcOcX RgIgL RSDVG RSLUN R]M`M`X\\[ R^E`H R`Xe[ RVSRZ ReCbJ RXE\\EZK\\Q\\TYT RcOkOjXgX',
	    'Po`HjH R^KkK R`PjP R`SjS R`[l[ R`E`H ReCeK R`MjMjU R`EjEjH RTDVF RRLVM RYO\\O[XY[ RcW_X RZE[H R`M`UiVkX RVSR[ R[Y`Ze[',
	    'Po_EcE R\\IbI RdRkR RZU`U RdUjU RZX`X RdXjX RX[l[ R_C_H RYR`Q`[ RdPd[ RdIgJlO RjHdN R\\HYL RaJ\\O RTDXF RRKWM ReEkEhK RVSS[ R[J^MYP',
	    'PoZFlF R[LjL R[H[L R^U^[ R`H`M RcNcQ ReHeM R[HjHkL RbCcF RTDXF R[N`O RRKVM RcTgXlZ RWRSZ RkTeV ReQkR RjNfO RbS^UXW RcY]Z R`QZR',
	    'PoZMlM R]RiR R_YgY Rh[k[ R[T[[ R]O^R R_W_Z R_WgWgY R]OiOiR R[TkTk[ ReGhG R\\G_G RTDXF RWSS[ RRLWM R[DbDbJ[J RdDkDkJdJ',
	    'PoXEaE RbGiG RaLlL RdUkU RdZkZ RY[][ R^N^Y RdPd[ ReCeL RcRkQk[ RSDVF R\\LaQ RhNjO R^QWW RhLaS R]EWK R\\LXQ RaJ]N RRKVM RkDgM RVSRZ R]K^U',
	    'PodFlF RXFaF RXLbL RdMkM RdQjQ RYR`R RdTjT RYV`V RdWjW R][`[ RYNY[ RZHZK R]C]F RYN`N`[ RdMd[lZl[ RSDVF RRKVM RUSS[ R_H_K RdCdIkIjN',
	    'PoXGaG RgIjI RcOjO RYQaQ RcRjR RcVjV RYKYQ R]C][ RYKaKaQ RcKcV RcKjKkV RhWl[ R\\RaV R[TWX RdWa[ RSDWE RRKUM RVSSZ R]PZV RfDdI RbDjDjI',
	    'PocElE RXIbI RdMkM RdQkQ RdVkV R^E^O RZQ_Q_Y RdIdV RhEfI RdIkIkV RSDVF RiWlZ R\\I`M RfWb[ R^IXO RRKVM RVSRZ RbW_Y RZQYY RaDYE',
	    'PoTE_E RbFlF RRJ`J RTN_N RVRhR RTW\\W RUG^G]N RVPhPhT RgKjN RVDRG RcIhK RXHZI RXLZL RcCaI RiHeLaN R\\WU[ RVPVThUcX R_TaWeYl[ R_T_[[[ RUGTN',
	    'PoXFbF RYIaI RjLlL RZN`N RXLXN R]C]I RXLbLbN ReDeK ReDjDjL RgVl[ RSDVF RYVaTaWbY RRLUN R_P[T RZWWZ RiUc[ R\\OYR RePhY RdOkOgX RUSS[ R]U][[[ R_RYR',
	    'PobElE RXGbG RcMkM RYQaQ RcQkQ RcUkU RYKYQ R]C][ RYKaKaQ RcIcU RfEfI RcIkIkU RiWl[ RTDWE R\\RaW R[TWX ReWa[ RRKVM RVSSZ R]PZV',
	    'PoWG^G RcJiJ RWN^N RaQeQ RWR^R RaUeU RWV^V Rc[e[ Rh[k[ RWJWR R[C[[ RWJ^J^R RaNa[ RaNeNe[ RhNhW RkMk[ RSDUF RRKVM ReDkJ ReE`J RVRRZ',
	    'Po[DjD R\\J_J ReJhJ R[M_M ReMiM RYRaR RcRkR RZGZK R^O^[ RbDbN RgOg[ RZGkGkK RTDWG R]SaV RiUlX R]TXX RdUaX RgPiV RRLVM RgPdW RVSSZ R^Q[V',
	    'Po\\ShS R[ZjZ RYLYO R[V[[ R\\OhOhS R[VjVj[ RYLlLlO RTDWF R]HaJ RgHlJ RlEjG R[EZG RaE_G ReEdG RRKVM RWRS[ RbSaV R\\IYJ RfIcJ R^C\\I R\\O\\S RhCfI',
	    'Po_DlD Rh[k[ RXKXQ\\Q\\Z R_K_[ ReDe[ R_KkKk[ RSDVF RaTcV RgTjW RgNiP RiMgP RaF^I RRKVM RhGlH RiSgW RVSRZ RaNcNaQ RcSaW R[[X[ RXD\\D\\KXK',
	    'PoYIkI RYLlL Rh[k[ RYDYH RgOgW RkMk[ RTDVF R_QdS R[N]P R[U]W RdNbP RdUbW RRKVM R_XeZ RVSRZ R_R[S R_YZ[ R_E_I ReEeI RYDkEkI R`M_R R`T_Y RYLXTWZ',
	    'PoZJaJ RXR\\R R^RcR RXMXR RXM\\M\\R R^M^R R^McMcR RSDVF R`UcX RZU\\X R]CbH RgWc[ R[FWK RgRjZ RRKVM RiQeY R\\CZH RUSS[ RZTW[ R`T][ ReJhV ReIkIhV RfCdN',
	    'PoZElE RZIlI RZPjP R^SlS R]W\\Z R_C_G R`I`P RbWbZ ReIeP RfCfG RZLjLjP RhViX RaGcI ReWeY RTDXF RRLVM RVSSZ Rj[g[ R^UkUj[ R^P^U RZIZTY[',
	    'PoZG`G RdGkG RZJ`J RdJkJ RdMkM RXSlS RZD`D`L RdDdL RdDkDkL RZDZM`M`O RiNkP ReNcQ R[NYQ RTDWF RRKWM RVSSZ R_Q^T[WjZ RgTcYX[',
	    'PoYGlG R[NiN R[QiQ RYTkT RXWlW R[K[Q R_Q_W RbKbQ RbCbJ ReQeW RYLjKiQ RTDWG RSKUN R\\D]F RhDeG ReXk[ RcHlL RVRRZ RbG_HXL R^XX[',
	    'PoXE_E RbFlF RcNjN RXY_Y RXEXY R\\E\\L RXR^R\\Y RXL_L_R RcIcN RaQeQe[ RcIjIjN RhQlQl[ RTDVF RRKUN RUSRZ RcCaH RhQhZlZ RaQaZfZ',
	    'PoZOjO RZRjR RYTkT RXWlW RZLZQ R^R^W RYDaDaJ RbLbQ RfRfW RZLjLjQ RcDjDkK RSDWE RRKVM RVSSZ R_HZJ RhEdG RhHdJ R^EZG ReYl[ R_YX[',
	    'PoYKlK RYO]O RZR]R RYU]U R`UdU RiZk[ R]K]Y R`K`Y RlWl[ RSDWE R\\F^H RbFdI RiEjH RRKVM R]DZJ RVSSZ RbD`I RdO`O RdR`R RgCgQhZ RkNgVcZYZ',
	    'Po]IgI RZP^P R[VjV Rg[i[ RZLZP R[R[[ RZL^L^P R_R_[ R`LdLdP RdRd[ R[RjRj[ RfLjLjP RSDXF RRKWM RcCeFlI RVSSZ RaC^GXJ RfLfPjP R`L`PdP',
	    'PocOkO R[ViV R[F[O RdJdO RYHiGiJ RZTiSiW Rc[lZl[ RRKWM R]CYH R\\EdEbG RTDXF ReXhY RVSSZ R^OZS[V R\\QdQaWc[ RaV^YX[ R[L^KjKcL R_OYO',
	    'PoYFcF RXIlI RYNcN RYQcQ RYTdT RXWeW RYLYQ R\\Q\\W R^L^Q R^C^I R`Q`W RYLcLcQ RSDWE RaYcY RhEjG RRKVM RiOdZ RVSSZ R\\YW[ RiMgW RlWk[ ReCeLgY',
	    'PoZFlF RdLjL RZOkO R[QjQ RYTlT R[L`L_T R[HaHaL RbCbF RdHeLfT RTDXF RRKWM RbTfYl[ RWRS[ RbTZX RjVfX R^V^Z RdHjHjL R[H[L RdY[Z',
	    'Po\\IiI R\\KjK RZNlN R^ThT RcWjW Ra[l[ R^P^T RcTc[ RZFZGkFkH RgGfNkS RbCeF R_G_N[S R]WY[ R]QhQhT RTDXF RRKVM RWRSZ R_Zf[',
	    'PoYHaH RdHlH RYKaK RdKlK R\\NiN R]UhU Rh[l[ RYDY[ R]Q]U RYDaDaK RbLb[ RdDdK R]QhQhU RdDlDl[ RbUgY RbT^Y RfRdS RSDWF RRKWM RWSS[',
	    'PoZFbF RXKbK RYOaO RZScS R_ViV R^[b[ RbVb[ RSDVF RgSjV ReJjO RiJcO RdGgL R\\L^L RRKVM RZVfWjZ RdGkFgM RVSRZ ReDcI R`PZR ReQZV R]XXZ RZCYHYN R[HaH`O',
	    'Po[DkD R\\J`J ReJiJ R\\MaM ReMjM RYRbR RcSkS RcWkW RcZkZ RZHZJ R]O][ RcDcN RcPc[ RcPkPk[ RZHlHlJ R]SaW R\\UXY RTDXF RRKVM RWRSZ R]Q[W',
	    'PoZGaG ReGlG RZJaJ ReJlJ R^PhP R^RhR R`ZcZ RZDZ[ R^N^R RZDaDaJ RcRcZ ReDeJ R^NhNhR RTDWF RfVhX RcTfV R\\UaU]X RiTfU RRKVM RVSSZ ReDlDl[j[',
	    'Po[MkM R[RbR RcRlR R[TbT R[O[[ R[ObObT R[JkJkM ReTfW RiFhI R]F]H RZEcEcH RTDXE RRKWM RWRSZ R[J[M R`J`M ReJeM R_VaYZ[ RjOj[g[ RjDdD',
	    'Po[E`E RdEhE R[H`H RdHhH R[J`J RcJiJ RZMjM RXOlO R[ViV RX[l[ RZDZM R[R[V R^D^M RbCbM RfDfM R[RiRiV RjDjM RTDWG RfWeZ R^X^Z RRLVM RVSS[',
	    'PoZDkD R]GhG R]JhJ R]MkM RYMhM RZR`R RdRjR RZU`U RdUjU RXX`X RbXkX RZPZX R]D]M RXP`P`[ RdPdX RhDhN RbPkPj[ RSDVF RRKVM RUSS[',
	    'Po[HiH R[KiK RXPaP RcPkP RXTaT RcTkT RZWjW RX[l[ RXMXS R[D[K R\\M\\S RXMaMaS RbDbK RbUb[ RcMcS RgMgS R[DiDiK RcMkMkS RTDWE RRKVM RVSS[',
	    'PoYFlF RZIbI ReLkL RZPbP RZSbS RW[l[ RZIZS RZUZ[ R^C^G R^I^L R^P^S R_U_[ RZLbLbP RdUd[ ReCeG RZUjUj[ RTDWG RRLVM RhOiR RVSR[ RfIdN',
	    'PoXGlG RYKkK RZNjN RXPlP RZS`S RZV`V RZY`Y RZPZY R`C`G R`P`[ RbKbP RdCdG RSDWE R^H_K R[D]F RiDgF RfHeK RRKVM RdUhYl[ RVSSZ RbSjSiVfYb[',
	    'PoZGaG RdGkG RZJaJ RdJkJ RZLaL RdLkL R[QkQ R[SjS R[WjW R[ZlZ RZDZL R\\O\\[ RZDaDaL RdOdZ RdDdL RdDkDkL RSDXF RRKWM R\\PWSSZ',
	    'PocIlI RdNkN RYO`O RdTkT RXVaV RdZlZ RYKYO R\\Q]V RXQaQaU RYQY[aZa[ RdGd[ RiGiZ RTDVG R\\F]J R_F`J RYFZI RRLVM RZCXG R]C\\G RVSS[ R`C_G ReDaL RYK`K`O RiDhK',
	    'PoZEbE RcElE RZIbI RdIlI R^LbL RhLlL RZNlN R\\SiS ReWkW R^YhY Rh[k[ RZUZ[ R[EZI R\\P\\S R_C_K RcYb[ ReEcI ReUfW RhChK R\\PiPiS RZUkUk[ RhHdL R_H[L RTDXF RRKWM RVSSZ R`V\\W',
	    'PoZElE RZJkJ R[TjT RYWlW R[[j[ RZ[g[ R_P`R RbRbW ReEeJ RZGkGjJ RSDVF RgXj[ RRKVM R\\P[R RUSS[ R_W^Z R\\NkNjRgR R_E^J\\N RZEYMXT',
	    'PoYElE R[MaM RdMkM R[QkQ R[TjT R[WjW R[ZlZ R[I[M R\\P\\[ R_C_G R[IaIaM RcOcZ RdIdM RfCfG RdIkIkM R]OXT RTDXF RRKWM RVSSZ',
	    'PoYHaH RdHlH RYKaK RdKlK R\\OcO R\\TbT Rh[l[ RYEY[ R\\Q\\T RYEaEaK RbQbU RdEdK ReOjOiR RdElEl[ RTDVF RfUiX RRLVM RVSRZ RfLdQ RdXbX RfQeW RbM_P^X',
	    'PocFkF RXI`I RbJlJ RXM`M RWOaO RcPkP RXU`U Rb[l[ RXFXM RXRXU R[C[M R^C^M RXR`R`U RXF`F`M RcSc[ ReSe[ RhSh[ RcSkSk[ RSDVF RjMkQ RZWZY RRLVM RUSS[ R^V^Z RgCgJdP RaYW[',
	    'PocElE RXGaG RXJaJ RXNaN RYT`T RXDXJ RYPYT RYP`P`T RXDaDaJ RdIdU RdIkIkU RTDVF R`VaY RRKUN RhWl[ RYVXZ RfXc[ R]K\\N RUSSZ RhEfI R]T][Z[ RhLgSfX',
	    'Po^RgR R[UjU RY[l[ RYLYO RbRb[ RYLkLkO RTDWG R]W^Z R]HaJ RkDjG RhWfY RbE`G RcNlR RRLVM RgHlJ R[DZG RVSS[ RdDdG R\\IZJ RfIcJ RaM_PYS R^C]I RhCfI',
	    'PoYDaD RcDlD RYMlM RYSjS R]VbV Re[l[ RZFZK R]S][ R]H]K R_M_S RZF`F`K RbKbM RdFdK ReMe[ RhHgK RYOjOjS RdFkFkK RlXl[ RSDWE RRKVM RVSSZ RkUeW RbZZ[ RYMYTXZ',
	    'Po[FaF RdFkF RcHlH RiMlM R\\QiQ R\\TiT R\\WiW R\\N\\W R\\NjNjW RTDWE R[DZG ReDdG RRKVM ReYl[ RVRSZ R^C]H[M RgCgHdM RYHaH`M^L R_YX[ RhHiM',
	    'Po\\DbD R[FcF R\\HbH RdKlK RRKYK R\\KbK R\\PbP RZ[_[ RVJVP R\\M[P R_Q_[ R\\MbMbP RiJiP RSFUH RdFgH RjEeJ RUCTG RXFSJ RgCfG RYLYO RkLkO R_RdWkZ RSLRP RfMdP RST[TWXRZ RiScV',
	    'PoXIaI RXMaM RWObO RdSkS RYUaU Rd[k[ RXFXM RYQYU R[D[M R^D^M RYQaQaU RXFaFaM RdJdZ RhJhR RcLkKkT RSDVF RlWl[ RZW[Y RRLVM RUSS[ RfDbL R^W^Y ReGiGgN RaZW[',
	    'PoXFbF RdIlI RYNaN ReNlN ReTlT ReZlZ RYRY[ RYHYN R]C]F RaHaN ReGe[ RiGiZ RSDVG R\\I_L R_H[M RRLVM RVSRZ R\\N[W RfCcM RYRbRa[_[ RiDiJ R^T`WZW',
	    'PoZFlF R[IbI RdIkI RfSkS R]UbU RfUkU R\\XbX RfXlX R_G_O R\\SbQb[ RfQf[ RgGgO R^IbM RgIlN RbDdF RhIcO R_K[O RSDVE RRKWM RVRRZ RZFZRX[',
	    'Po`FlF RdMhM RaOkO RXQ^Q RbUkU RbXkX R`[l[ RXHXS R[C[X RXH^H^Q RaIaO RbRbX RdKdM RdJhKhM RbRkRkX RaIkIkO RSDVG RRLUN ReDgF RUSR[ R^T^XWY',
	    'PoXFbF RdIlI RZI_I RXKaK RdNkN RXPaP RXRaR RdTkT RXUaU RdZlZ RXKXP RZCZI R_C_I RdGd[ RiGiZ RSDVF R]I\\V`Z R\\WX[ RRKWM RVSS[ ReEaN ReCbL RiDhL',
	    'Po\\LhL R[RjR RZTjT RYWlW RYGYJ R[N[Q R\\I\\L R`Y`[ RbCbF RbNbW R\\IhIhL R[NjNjQ RYGlGlJ RiXl[ R]D]G R[YY[ RhDfF RdYe[ RSDWE RRKVM RVSSZ',
	    'Po[G^G RgGjG RfJjJ R\\QiQ R\\TiT R\\ViV RYLYP R\\O\\V R\\OiOiV RfDkDkL ReVe[lZl[ RTDWG RdDaG RRKVM R[EZLlLlP R`DdF RVSSZ RaIdJ`K R^YZ[ R^DZE R^JZJ',
	    'PoYGaG RdGlG RdKlK RYKaK RdOiO R]ThT R]VhV R\\XiX Rh[l[ RYDY[ R]Q]V RYDaDaK R\\MaMaO RcQc[ RdMdO RdDdJ R\\M\\O_PhQhV RdMiMiO RdDlDl[ RSDVG RRLVN RVSR[',
	    'PoXEkE R[HjH R[NiN R_RdR RYS]S R_UdU RYW]W R_WdW R[[][ RZE[H R[J[N RYP]P][ R_P_W R_PdPdW R[JiJiN RfPjPkYl[ RSDVF RaY_[ RgRhV RRKVM RVRRZ RYPYVXZ RfPfVe[',
	    'PobElE RYHaH RYKaK RcMkM RXObO RcRkR RYU`U RcVkV RYEYK RYQYU R\\K]O RYQ`Q`U RYEaEaK RcIcV RgEfI RcIkIkV RSDVF RRKVM R`WaY RiXl[ RYWWZ ReXb[ RUSS[ R]U][Z[',
	    'PoZDkD R[I`I ReIiI R[L`L ReLjL RXOcO R[Q`Q ReRkR ReWkW RYVbV RXYbY Rh[k[ RXGXJ RYSYV R[N[Q R^Q^[ R`N`Q RYSbSbV RbDbL ReNkNk[ RXGlGlJ RTDVF RRKVM RVSRZ ReNeUd[',
	    'PoZGlG RfKkK R^PkP R^WhW R\\ZiZ R^M^P R^S^W RcRcY RdHdL RfMgP R]UhUgX R^MkMkP RZDlDkG R_SlSlZ RTDWG RRKVM RbMbP\\U RVRSZ RkIfI Rk[i[ R]IbJ]K RZDZRYZ',
	    'PoZDaD RdFlF ReGjG RZJaJ RZMbM RcMkM R\\RiR R\\TiT R\\WiW RZDZM R\\O\\W R^D^G R^J^M RZGaGaJ RdJcM RfJfM RcJjJhM R\\OiOiW RkJkM ReWe[lZl[ RTDWE RRKVM RVSSZ ReCcH RaW^ZY[',
	    'PoZElE R[KaK RdKkK RdNkN R[NaN R]PiP R^VhV Rh[k[ R[I[[ R_C_G R[IaIaN RcOc[ RdIdN RfCfG R^RhRhV RdIkIk[ RTDWG RcUgY RcU^Z RRKWM RWSTY R^R^V',
	    'PoZEkE R\\IiI R\\NaN RdNjN RZQkQ R[SjS RZUlU R^W][ R_P_U R\\L\\MaLaN RbCbJ RfPfU R\\F\\HhFiI RdLdMiLiN RYJYLlJlM RjVfX RTDXF RRKVM RcUgYl[ RWRSZ RbUZY RcY]Z',
	    'Po_DdD R^FeF R_HdH R_OdO R[UiU Rc[h[ RXLXO RXK\\J[P R]L]O R_L_O R_LdLdO RfLfO RZQiQiT RSDVF RhCfFiHfJjJjNlO RRKVM R[TZWjWh[ RZCZFZJ RVSRZ RaJdJ RbJ_J',
	    'Po_DdD R^FdF R_IdI R_KdK R_MdM RXTlT RXK\\K[Q R_M_QcQa[ RhKhQ RSDVF R[CYG[HZK R]M]O RkMkP RhCfGiHfKkJkK RRKVM RcTfWlZ RfMeP RXMXP RVSRZ RaS]XWZ',
	    'PoVIhI R\\N_N RV[X[ Rc[e[ RYTY[ R_I_N ReTe[ ReUjY RYU]Y RSVVWRZ RTT]U[X R_VcW_Z RaTjUhX RTPTT RTP]P\\T RaPaT R_JdLkN RYR]R RdGhG RTK\\KXMSN RiJcK RWRTR RdR`R RaPjPjSfR RWRZR RZGVFWGZG\\GYG RVEhEhGdG R]G`GcG_G\\G',
	    'PocElE RXHaH RXLaL RdMkM RdQkQ R]UbU RdVkV RXEXK R[W[[ RXEaEaK RdIdV RgEfI RdIkIkV RSDVE RRKUM RiXlZ RfWb[ RYMXP R_M]Q RaP]T R[PWU\\T\\U R^W^Z RaVbY RVSRZ RXWV[ R[PWP RaP]Q',
	    'PoXMaM RWObO RdRkR RYVaV RY[b[ RX[`[ Rd[l[ RXDXM RYRYV RZDZK R\\D\\M R_D_K RYRaRaV RaDaM RdKd[ RhJhR RdKkKkR RSDVF RRLVM RlXl[ RZWZZ ReGjFgL RVSR[ RfCcJ R_W_[',
	    'PoXF`F RdGkJ RWK`K RcKjK RbNkN RXP_P RcSjS RXS_S RcUjU RWW`W RcXjX RXMXS R\\S\\[ RXM_M_S RcPcX RfKgN RcPjPjX RhYl[ RSDVG R[C\\F ReCbG RZGZI RRLVM RVSRZ R^G]J ReYa[ RdFjEhHcJ_J',
	    'PodFkF RcJlJ RXMaM RWObO RdPkP RXUaU Rb[l[ RXDXL RXRXU RZDZL R\\C\\L RXRaRaU RaDaL RcSc[ RfSf[ RcSjSi[ RkSk[ RSDVF RSKUN RZWZY RjLkQ RUSS[ R^V^Z RgCgJdP R_D_K]J R`F]F RaYW[',
	    'PoXF]F RfFlF R^HeH RZUcU RZWbW RXMXP RZPZT RZVZ[ R[C[K R_EcEbK RcPcT R_I_KdJdK RiCiK RXMlMlP RSDUF R_QaS RRLUM RYF]I RhFlJ R`C_F RZDXJ RUSRZ RiDfJ R\\O`P[R RjOeQ RjSeU RjXd[ RbZY[',
	    'Po_D_ObTeXl[ RjIgO RWITP R_N\\TXXR[',
	    'Po[T[[ RjWk[ RgTjX RaTc[ RVTR[',
	    'PoTEjE R_E_NcUfYl[ RhKfP RXKUQ R^O\\UXXR[ R_JbT R_H]S',
	    'Pod[k[ RdOdZ RiFfJ RlVl[ RXGTJ RYVVYR[ R_EaKdNkP R_C]KZMWORP RZOYW',
	    'Po^FlF R`[f[ RfFf[ R\\IZM RTISN RWCWOWTSZ RWS\\W',
	    'PoUElE RaHeVhYl[ RiKgQ R[LXR R^UZYV[ RaJ^U RUEUQSZ',
	    'Pod[j[ RWCWS[W R_UZ[ RVSS[ RbO^X RSHSO R[HZN R]FlFlRj[ RWMUW RcFaT',
	    'Pof[l[ RdGdU RlDl[ R[T_X RYDXR\\V RWVR[ RUIRO RXRUY R^I\\N RYLWV',
	    'Pod[j[ RaSfX R^QaS R]SWY RXGRL R^O[V RgLdQ RXLWR RYCWHlGlSj[ R_J]S',
	    'Po^FkF R]ZlZ RdFdZ RWCWS[W R[HYM RVSS[ RSHSO RWNUW',
	    'PoSHkH RVLgL RVDgDgM RiPeT RXRTU R_OaVeYl[ R_N]UZXWZR[',
	    'Po^LkL R\\ZlZ RdCdZ RWCWS[W R[HYM RVSS[ RSIRN RWMUW',
	    'PoRSlS RdOd[ RiFfI RYVU[ R_GcKkO RXFWJ R_D]J[LXNRO RZPYW',
	    'PohQdU RXQUU R`IcLkO R_ObVdXj[ RZFbF]LSP R\\CXHSJ R_N]UZXWZR[',
	    'Poc[k[ RdFdZ RWCWS[W RlVk[ R[HYM RVSS[ RSIRN RjD]G RWMUW RgN^O RjNaO',
	    'Po_[k[ R_F_Z ReCeU RlVkZ RWCWS[W R[HYM RVSS[ RdK[N RkIdK^M RkHkTgT RSIRO RWMUW',
	    'PoUGiG RRLlL R_C_L RiPeS R_OaUeXl[ RXOWT R^TZXT[ R_M]V',
	    'Po_QhQ Rd[j[ RWDWS]X R\\HZM RWRTZ RbC^N RTISN R`IlIlPlSj[ RWMVW',
	    'PoTHXL R\\HaL ReHjL RiDeH RiPeT RXDTH R`C\\G RWQTT R_O\\VZXT[ R_ObVeYl[',
	    'PoSGSL R_C_G RSGkGkL RhOeR R`PbVeYk[ RXMVR R]TYXR[ R_KbU R_L\\U',
	    'Po^YlY R^J^Y ReCeY RlJl[ RWCWS[X R[HZM RSISO RWMVW',
	    'Pof[j[ RWCWS[W RaO[U ReS^Z R\\EhEdH RhLdU R[IYM RWSSZ RbL_R RSISO RcI]LlLlRj[ RWMVW',
	    'PoVEjE RRKlK R`SkS RVEVK RaMaS RgVl[ RkPkS R[V[[ RaVc[ RVVTZ R]NZQTT R_E]N',
	    'PoWCWS[W RhJlM RfDjL R[HYM RVSS[ RaD]M RcN]Z RSIRO RWMUW RgYcY]Z RhTjZdY',
	    'PoaQeQ RiReV RXSTV RcDfGlI R]KZOUQ RZDWHRI R_Q]VZYR[ R_R`VdYl[ RWJfJfP',
	    'PoZYlY RiKiR R\\EjEjN RWCWS[W R[HYM RVSS[ RSIRN R\\NiNiThZ RWMUW RbGaMaQ_Y',
	    'PoUIiI RUNiN RUDUN RUDiDiN RiQeU RXQVU R_PaVeYl[ R_N]UZXWZR[',
	    'Po\\IlI RdCdI RWDWS[X R_KcThYl[ RWSSZ RiKeT`Y[[ RTISO R[HXMVW',
	    'Po_IlI RWDWS[W R`McUhYl[ RWSTZ R`F\\O RiLeT`Y\\[ R_IbS RTISN RaC^M RiIfS R[HXMVW',
	    'PogLlL R[PiP R_DgDgL RWCWS[W R_J[N R]P_ScWk[ R[HYM RVSS[ RiQcW[[ RSIRO RWMUW R_E_K',
	    'Po]ElE R_QkQ R_JkJkS R[HYM RTISO R_J_R]Z RWDWPSZ RWQ[U',
	    'Po[HYM R_IkIiN RTISO R`C^N RWDWQSZ RWR\\V ReIdQaW][ RdQgWkZ',
	    'Po^FlF ReJe[ RWCWS\\W RbO]T RgMkQkS R[HZN RWSSZ RfFaQ RRHTO RWMVW',
	    'PohMh[ RWCWS[X RfGlM RcG\\M RdDfH R[HYM RVSS[ RdCbI RSIRO RWMUW RaMaS`X',
	    'PoWCWSZW RaMdUhYlZ R[HYM RVSS[ R^LjLgTbY^[ RTISO RWMUX R^F^R]Y RkD_F',
	    'PoiEeI RiQeU RXFUJ RWRTU R_D]JZLTO R_P\\VZXT[ R_DaJcLfNkO R_OaVdYgZl[',
	    'PofVj[ RWCWS[W RbV^Z RkIhO R[IYM RVSS[ RcRaX ReCdPfW R`I^P RSIRO RdLeU RWMUW RdKcV',
	    'Po\\HlH R]NkN R\\TlT RcCc[ RWCWS[W R[HYM RVSS[ RTISN RWMUW',
	    'Po]IkI RdCd[ RWCWSZW RhRlV R_S\\V RdHiS R[HYM RVSS[ RcI^U RSIRO RWMUW',
	    'PoaRdR RdCdR RiGlN R[HYM R`G]O RiPgUdX][ RTIRO RWDWOVUS[ RWRZV',
	    'PohCh[ RWCWS[W R^MdO R`FeH R[HYM RVSS[ RSHSO RWNUW RfT^U RhT`U RlSdT',
	    'Po\\PlP R]HjHjP ReUk[ RWCWS[W RaU\\[ RcCcQgX R[HZM RVSSZ RcP`X RTISO RWMUW RcLbU',
	    'Po\\GkG Rh[l[ RcCcG R_MhMhZ RXCWS\\W RlWl[ R\\IZM RTISO RWMVW R_M_S^X',
	    'Po]SjS Rd[l[ R]K]U RdCd[ RkKkU RWDWS[X RlXl[ R[HYM RWSS[ RTIRO RWMVW RkF]H',
	    'PoRGlG RYLdL RYCYL ReCeL RiPeT RWQTT RWOVS R]TZXT[ R_MaUeYl[ R_N\\V',
	    'Po_MlM RgMg[ RWDWR[V R[HYM RWRSZ RTIRO RWMVW RjD_F_Q]Y',
	    'PoiQeT RXPVT R[FhFcJZN R^ERJ R_ObVeYl[ RZG^IaLTP R_N]UYYR[ RUIYJ\\NRP',
	    'PoRMYM RRVYV RRERX RREYEYV RfVj[ RkIhO RbV^Z[[ RdCdOfW R_I]P RdKbV',
	    'Po`[k[ RYQY[ RaNaZ RiFfI RkWk[ RYFTJ R[NVSRU RiP\\V R_DaIdLgMlN R_D]J[LWMRN',
	    'Po]GkG R^LjL RhXkX RdCd[ RWCWS[W R[HYM RVSS[ RTISO R\\QlQkX RWMUW',
	    'PobTgT R_[j[ R`M`Z RVCVTZW RdElM RjVjZ RcCbG[M RVSR[ RTISO RZHYM RVNUX R_MhMgT',
	    'Po]HlH RdHd[ RVCVSZW RcCeG RdNkQ R[HYM RVSR[ RSIRN RVMUX',
	    'Po^MjM R^UjU R[ZlZ R^E^U R^EjEjU RWDWS[X RWSSZ RTIRO R[HXMVW',
	    'Po^LlL R^SlS R^E^T ReEe[ R^ElElT RWCWS[W R[HYM RWSSZ RSIRN RWMUW',
	    'Po]ElE ReOkO R\\[l[ R_L_Z ReEeZ RWDWS[W R[HYM RWSSZ RTISO RWMVW',
	    'Po]JkJ RdCd[ RhSlV RfEiH RWCWSYX RdIfNiT RZHYM RVSS[ RdI^U RSIRO RWMUX',
	    'Po^ElE R^TeT Rc[i[ R^J^U R^JeJeT RiEi[ RWDWS\\X R[HZM RWSSZ RSHTN RWNUX',
	    'PofNjN R^ZkZ R^Q^[ R^QkQk[ RXCWS\\X RcJ^O R\\HZM RdFaM RTISN R^ElEkN RWMVW',
	    'Po]MkM RfCf[ R\\GkGkM RWDVTYW R`W\\[ RYHYM RVSR[ RTISO RkXhX R\\M\\RlRkX RWMUX RaCaQ`W',
	    'Po]JlJ RWCWS[W RfDjG RaCaOdUhYl[ R[HYM RVSSZ R`S\\[ RaNiNfUbY^[ RSIRO RWMUW RaJ_W',
	    'Po^HlH R_[k[ R_K_[ RdIdW RjLjU RVDWTYX RlXk[ RYHXM R`C[L RTISO RjM[Q RgUjUgU RVNUX',
	    'Po`[k[ R]H]K RaKaZ RlWk[ R]HlHlK RWCWS[W RcDeF RZHYM RVSS[ RiN`S RSIRO RWNUX',
	    'Po`Uf[ RWCWS[W RcJdN RcDlL R^PjQcX RcCbG\\L R[HYM RVSS[ RSISO RWNUW',
	    'Po^MkM R^YlY R^Q^Y R^F^M ReCeY RkFkM RlQl[ RWCWS[W R[HYM RWSSZ RSIRO RWMVW',
	    'Po\\HlH R^ZkZ R\\ZgZ R_NcS RWCWS[W RjWk[ RgTkX RfPcR RbU]Z RgLfP R[HYM RVSS[ RdCcH`M RTISO RWMUW',
	    'Po_ElE R_SjS R_ZlZ R_E_[ R_KjKjS RXCWS\\X R\\HZM RTISN RXMVW',
	    'PoUIjI RULlL RUDUH R_C_H RjDjH RiPfT RZQWT RcVeYk[ R_V\\YW[ RaNcV R`N_V RULUTSZ',
	    'Po_SfS RfVjV R_[k[ R_K_[ R_KfKfR RWCWS[W RlWk[ R[HYM RaC\\L RTIRO R_HkHkPjU RWMUW',
	    'PoaThT Rh[l[ R^E^[ RaIaV RaIhIhT R^ElEl[ RWCWS[W RWSSZ RTISO R[IXMVW',
	    'PoWNbN RdPhP RWTjT RWIWT R\\W\\[ RWIbIbN RhWl[ RVXR[ RXCWGRK RbWc[ RlQjT RWFjFhP',
	    'PoSHiH RVOhO RVKVO RVKhKhO RhSeV R[CWH RcEkI RWRVV R_QaVdYl[ R_Q]VZYR[',
	    'Po\\MhM R^ZjZ R^Q^[ R^QjQj[ RWCWS[W RjKlN RgHjK R`I\\M R[HYM RVSS[ RcC`I RSIRN RWMUW RjLfM',
	    'Po\\ElE Rh[k[ R]J][ R]JlJl[ RWDWS\\W RdEdMhS R[HYM RWSTZ RcM`T RTISO RdHfQ RWMVW RdGcQ',
	    'Po\\QlQ R^H^Q R^HjHjQ ReVj[ RWCWS[W RbU]Z RdDcRgX RWSSZ RTIRO RZHXMUW RcMbV',
	    'PoVCVSZW R`F[K R^HcNlQ RZHXM RaC^I R_GjGcM[Q R`ShU R]WiZ RSISO RVNUX',
	    'PodIlI R^ZjZ R^P^[ RdCdP R^PjPk[ RWDWS[W R[HYM RWRSZ RTISO RWMVW',
	    'Po^IlI R^QkQ R\\ZlZ ReCeZ RXDXR\\W R\\HZM RTISO RXMVW',
	    'Po_HlH RcMlM RcSlS RcHc[ RWCWS[W RZHYM RVSS[ R`D\\M RSIRN RWMUW',
	    'Po_GkG RVSiS RVLVS R\\W\\[ R_C_L RVLiLiS RhVlZ RVWR[ RbWd[',
	    'Po\\U\\Z RTHgHfM ReUgW RXD[F RaUbY RWUU[ RaC]LTT R\\MiMiR RWRlRk[g[',
	    'Po\\ElE R_YjY R`M`[ R_OjNj[ RWCWS[W R_N[S R[HYM RVSS[ RbF]Q RSIRO RWMUW',
	    'Po[GlG R\\QkQ R`[d[ R\\KdKd[ RWCWTYX R_T]X RiTkY RYHYM RVSR[ RSIRO RaC^O RVNUX',
	    'Po^PkP R^E^P R^EkEkP RWCWS[W RhTk[ R[HYM RWSSZ R`T^Z RSISO RWMVW',
	    'Po^NjN Rf[l[ R^E^N RgNg[ R^EjEjN RlWl[ RWCWS[W R_W[[ R[HYM RVSS[ RSIRO RaN_X RWMUW',
	    'Po[OlO Rf[i[ R^GjGj[ RWCWSZW RbIdL R[HYM RVSS[ RdCbG RSIRN RWMUW R^G^R]Y',
	    'Po[QlQ R_[c[ RdEd[ RVCVSZW RjHgO RYHXM R]I_O RSISO RVNUX RiD_F',
	    'Po_EfE R]NlN Ra[d[ ReHe[ RWCVSZW RiRlY RZHYM RVSS[ R`R]Y RTISO RVMUX R_E]N RjDaE',
	    'Po]JkJ R_QiQ R\\ZlZ RWCVS[W R`DaH RZHYM RVSS[ RhDfJ RSIRO RVMUX',
	    'Po]QkQ R\\ZlZ RdQdZ RWCWS[W R]EiEcKlN R[HYM RVSS[ RdJ[N RSIRO RWMUW',
	    'Po\\GlG RVCVSYW RgJkN RcCdF R`J\\O R`ObTgYl[ RYHXM RgOeT`Y\\[ RSIRN RVNTX',
	    'PoZZlZ R\\H\\Z RbFbZ ReHeZ R\\HjHjZ RVDVTYW RZHXM RVSR[ RRHSO RbDaK RVNUX',
	    'PocEkE RbLlL Rd[h[ R`L`[ RhLh[ RaD[I RaJ[P RVCVSXW RYHXM RSHSO RVMUX',
	    'Po^MjM R^SjS R\\ZlZ RdMdZ RWCWS[W RdDlL RcDbG\\M RVSS[ RSIRO R[HXMUW',
	    'PoSE`E ReTjT R\\W\\[ RcEcQ RjDjT RhWl[ RXGRM RbWc[ RTKYM RVWTZ RXEVI^I\\OXSSU',
	    'PoVIVP RTJiJiP RaLeO RhReV RZCSK R]LYP RXFcF`I RXRVW R_QaVdYk[ R_Q]VZYR[',
	    'Po^IlI R^NkN R]TlT ReIe[ RXDWS\\X R`DaH R\\HZM RWRTY RiCgH RTISN RXMVV',
	    'PoZHlH RWGWS R[W\\[ RbCbS RhWkZ RdJjQ RbGdK RWHRM RaJZQ RbWc[ RYCVJ RVWTZ RbG`L',
	    'Po`JkJ R^G^[ RfCf[ RVCVSYW RgMkW R_EZO RdN`W RSHTO RfHiT RYHXM RfHbT RVNUX',
	    'Po^IjI R\\OlO Rf[l[ RdCdO RgOg[ RlXl[ RWDVSZW R_W\\[ RZHYM RVSS[ R`E\\M RSHTO RaO_X RVNUX',
	    'PoTEjE RSJkJ RdEdP RhQdU RYRUV RZKWOSP R_Q`UcXgZl[ R_Q]VZXWZR[ RZFZM',
	    'PoVLgL RVOlO Rd[i[ RVFgFhL RcUeX R^C]F RYVZZ R^V`Y RUVSZ RVFVSkSj[',
	    'Po[OlO Rf[i[ R^GjGj[ RcSeV RWCWSZW RbIdL R[HYM RVSS[ RdCbG RSIRN RWMUW R^G^R]Y',
	    'Pof[l[ RfDf[ RlXk[ RWDWS[W RgPkT RaQ]R R`W[[ R]G_L RjGhL RVSSZ RTIRO R[IXMUW RaDaQ`W',
	    'Po]IlI R\\OdO R`O`X RWDVSZW RhDjG RlWk[ R[HYM RRHSO ReW\\X RfCfMhY RVNUW',
	    'Po^HkH R]NlN R^TkT R[[l[ RdCdN RdPdZ RXDWS\\W R\\HZM RTISO RWMVW',
	    'PoaIiI RbUhU Rh[l[ R^E^[ RbMbV RbMhMhT R^ElEl[ RXCWS\\W R\\HZM RTISN RWMVW',
	    'PoUHlH RWLbL R\\M\\T`X RfDkE R[VWZ RgT`[ RjMeV RXOWS R`O_S RlVk[i[ RdCdMfX RUHTSSZ R\\P[W',
	    'PoTF_F RRKlK RZCZK RZT^X RgDkH RXUTZ R_O^R RhR`Z RgZj[ RiNgS RUPST RcOfY RlWk[ RcCcKeW RYNXV',
	    'Po]HlH R^OfO RfUl[ RWCWS[W RbK^O RdDeF R[HYM RVSS[ RcHaM RhLbS]T RkPdW\\[ RSISO RWMUW',
	    'Po\\JlJ R\\SlS R`D`R RgDgR RgVl[ RWCWS[W RaV\\[ RZHXM RVSS[ RSISO RWMUW',
	    'Po_ZjZ R_S_[ R_SjSj[ RWDWS\\W R`H\\K R`GbJgPlR RWSSZ RaC^J R`GiGhJbO\\R RTISO R[HXMVW',
	    'Po_LhL R^ZiZ R^Q^[ R^QiQi[ RWCWS[W RdFlM RdCfH RbF[M RZHYM RVSS[ RcCaH RSIRO RWMUW',
	    'Po]RjR R]I]Q R]IjIjQ RWCVSZW RiTl[ RZHYM RVSR[ RTISO RVMUX RcCdY`Y\\Z RjXcY',
	    'Po]ElE R_PjP R_VjV R[[l[ R_J_V R_JjJjV RXDWS\\W R\\HZM RTISO RXMVW',
	    'Po[R_R RVThT R[W[[ R_H_Q RhWl[ RUXS[ RbWc[ RbGdMhQlS RUEfE_H RSI[IYNUQRR RjHhJcL',
	    'Po\\IcI R`C`[ RfCf[ R_JcO RfJlP RVCVSYX RYHXM R`I[S RSIRO R`G]P RVNUX',
	    'Po`LjL R^ZlZ R^D^[ R^DlDl[ RWDWS[W ReGeOiV R[HYM RWSSZ RdObV ReKgU RTISO ReKdT RWMVW',
	    'Po]GlG R`PjP R`UjU Rf[j[ R`J`[ R_MjLj[ RVCVSZW RZHXM RbF]P RbC_M RSIRO RVNUX',
	    'PoTQjQ RSUkU RZNZU RcNcU RiEeH RXFUI RcWk[ R[WS[ R_FaIfLlM R_C^H[KRM',
	    'Poc[k[ R\\G\\K RcLcZ RcCeG R\\GlGlK RWCVS[W RlWk[ R[IYM RVSS[ RSIRO RiJ]M RWMUX RgQ]S RkQ`R',
	    'Po]GlG RgWk[ RWCWS[W RdV`Z RfJfShY RkLiR R[HYM RVSS[ R`L\\T RaG^R RbMaR RSIRO RbD_P RWMUW RfMeW',
	    'Po]GhG R\\LlL R_PjP Rd[h[ RcCcL RWDWS[W R[HYM RWSSZ RkDjGaO[R RTISO RaO_UjUi[ RWMVW',
	    'Po^ElE R^SjS R^ZlZ R^E^Z ReEeK ReSeZ R^KjKjR RWCWS[W R[HYM RVSS[ RSISO RWMUW',
	    'Po]ElE R_J_U R_JkJkU RfWjY RWCWS[W R[HZM ReEcI ReLdVaY][ RTISO RWMVW',
	    'PodGjG RaGhG R]HdH R[RlR Rf[l[ RfRf[ RkLkO RlXl[ RWCVS[W R`W\\[ RaFcKfNiO RZHYM RVSR[ R`CbK RTISO RaR_Y RiJcM]N RVMUW',
	    'Po[TlT R`G`P RcQc[ ReCeOkNlO RVCVSZW RYHXM R`C]J RjEfJaL RSIRO RVNUX',
	    'Po_MhM R\\QkQ RdYjY RfUjZ RVCVSZW RcDjK RcCbG\\L RYHXM R`U^Z RbQ_X RSIRN RVMUX RgYbZ\\Z',
	    'Po]IkI R\\PlP RfUk[ RWCWS[W R_DbG RbU\\[ RiDdIdQgX R[IYM RVSSZ RdP`X RSIRO RWNUX RdKcT',
	    'PoeRhR RdM`Q RgTeW RXMSQ R_KZP RXSUW RTDXE ReJcO RRIWJ R^R^U[YR[ R_UcYl[ RZDgD[JjJiQ',
	    'Po^KjK R^DjDjK RVQZU RfLkV R`PfR R_VhZ RZHXM RVCVTS[ RTISO RVNUX R^D^N\\U',
	    'PoSG[G RTUWU RiTkU RXCXT R\\W\\[ RlRlU RhWkZ RUXR[ R`P\\T RaWc[ R^MbOeQ R\\LRO R]GhGhPiU RbCbJ`Q',
	    'PoSDaD RfOkO RdEdL RkCkO RVJZK RhQdU RXRUU R_RdXl[ RWH_H[LTO R_P]UZXT[ RZDWHRJ',
	    'Po[LlL RgYjY R^P^Y RdNd[ R]RjQjX RVCVSZW R`N[S RYHYM RVSR[ RbI^Q RiD]J R]DkI RSIRO RVNUX',
	    'Po\\SlS R`[d[ RdQd[ RWCWS[W R]G_K RcGdK RjFgK R[HYM RVSS[ R^MjMgOdP RSIRO RWMUW RjC^E',
	    'PofKlK RaUhU Rh[l[ R]E][ RaOaW RgEgK RaOhOhU R]ElEl[ RWCWS[W RbJ_M RTISO R[HXMVW RbEaK',
	    'PoRG]G R`JlJ RTPXP RXCXP RgIgP RiReV RXSTV R_QaVdYl[ R_Q]VZYR[ RjD`E R`G_N R]JRK',
	    'Po\\GlG R]QkQ R]UkU Rg[k[ R]L][ RdCd[ R]LkLk[ RVCVSZW RgDkE RSHSO RZHYN RVNUX',
	    'Po]ElE R]SkS R[[l[ RdSd[ RVDVTZW RcMeQ RgLjP R]K_P RjGhL R_G]L RdGbL RSHSN RZHYN RVNUX',
	    'Po[FlF RTITO R`C`I RhChI RaPeU RVCVTZW RZHXM RVSR[ R_J\\Q Rh[d[ R^NjMjRh[ RVNUX',
	    'PocMkM RbVlV Rc[l[ RgEgV RVCVSXW R_R\\Z RSISO RZHXM R[E`E\\M`M^W RkDbE R\\Q^VaZi[ RVNUX',
	    'Po_LiL R\\QlQ Rf[l[ R]G]J RgQg[ R]GkGlJ RlWl[ RWCWS[W RcCdF R`V\\Z RVSS[ RaQ_X RSISN RZHXMUW',
	    'Po^GkG R[QlQ R]XlX Rd[h[ RaSeT RWCVSZW RbMfO RZHYM RVSR[ R_C\\K RSISO R_KjKjUi[ RVNUX R_K]X',
	    'PoSElE RWLgL RYU_U RWHWL R[W[[ R_C_F R_Q_U RWHgHgL RhWl[ RUXR[ RbWc[ RTOiO_Q',
	    'Po^KiK R^PiP R^G^Z RcCcG R^GiGiP ReUjZ RWDWS[W RiTeV RkRhU RcPfW R[HYM RTIRO ReX[Z RWMUW',
	    'PogMkM RbVlV Rb[l[ RbKbV RgEgV R]VY[ RVCVSXW R^R[Y RTISO RZHXM RkDbF R[E_E[M_M]W R[Q]V`YhZ RVNUX',
	    'Po]EkE RePeX RWCWS[W R`O\\Q RbL_P RdP^W RaJ\\L RdE_K RjIfNlW R[HYM RVSS[ RSISO RdZ`Z RbJdOeU RWNUW',
	    'Po^QjQ R^TjT R\\WkW RdNd[ RWDWS[X RaE\\J R`GdLlO R[HXM RVSS[ R`GiGcL\\O RTIRO RWNUW',
	    'PoUViV RR[l[ R_S_[ RhEeH RhMeP RYEVH RYMVP R`C^HkK R_McRkT R_F\\JTL R_J^N[QST',
	    'PoZOdO R_[b[ R\\EbEbZ RVDVTYW RkDfI RkKfP R_H_M RYHXM RVSR[ RTIRO RlSfYdZ RVNUX R\\E\\S[Z',
	    'Po_ZjZ R_R_[ R_RjRj[ RWCWSZW ReKlR RdHfM RgDkI RdHbL\\R R`F\\I RVSS[ RSIRN R[HXMUX',
	    'PoSMZM RSESM RSEZEZM RhNjP RiReV RXRTV RbKgM R^GkGiK R_C\\I RcGaL^O[P R_QaVdYl[ R_R]VZYR[',
	    'Po`GlG RWFWQ R[G[O RiSfV RYERK RXSUW RaF]J R`JkO RaC_I RhIdM]P R_ScXgZl[ R_Q]V[YS[',
	    'Po]GjG R[MlM Rb[f[ RWRXV RcCcM RfMf[ RiPlV RZHXM R]P[V R^Z\\[ RTIRO RWDWPUW RaN`X',
	    'PoRHlH RUKhK RYTiT RZMZT RUDhDhK RXOiOiT RgVl[ RUXR[ R[V\\[ RbVc[ R^DZMVQRR',
	    'Po^HkH R[MlM R^ZjZ R^R^[ RdCdQ R^RjRj[ RWDWS[X R[HYM RTIRO R_D]K RWMUW',
	    'Po`JkJ R_RlR R_VlV Rh[l[ R_N_[ R`D`J R`DkDkJ R_NlNl[ RXDXS\\X RVWR[ R\\HZN RTISN RXMVW',
	    'Po_QjQ R_VjV Rf[j[ R_L_[ RdCdL R_LjLj[ RWDWS[W RhFlJ R`F^J R[HZM RWSSZ RTIRO RWMVW',
	    'PobJjJ RTM^M RbOkO RUH^H^M RbDbO RbDjDjJ RkLkO RhSeV RXTUW R_QaVdYl[ R_Q]VZYR[ RUDUKTP R]DTE',
	    'PoSEkE R_IiI RRMlM RWQjQ Re[j[ RZWZZ R_E_L RdVfX R_V`Y RUVS[ RVTkTj[ RXHXMVT',
	    'Po^HjH R^MjM R]PkP R\\UlU R^D^L RdPd[ R^DjDjL RWCWS[W R[HYM RVSS[ RSISO RWMUW',
	    'PoTH[H R]HlH RRK[K R^OkO RTDTH R[C[P RdCdO RiReU RXRUV R_QaVdYl[ RUKTP R_Q]VZYR[',
	    'Po\\IkI RWDWSYUZX RgElJ RbP\\U R`SdXl[ R[HYM RVSSZ RbC_I RbI`N\\O RfIfOlMkO R`RiSeW][ RTIRO RWMUW',
	    'Po]MkM RdHd[ R\\HkHkM RVCVSZW RhCgH R_C`G RZHXM RVSR[ RdR^XYY RkXgX R]M]RlRkX RSIRN RVMUX',
	    'PoSH]H R_KlK RTTXT RXCXT RgKgT RgVl[ R[V[[ RaVc[ RUVSZ R_F_L^Q RiD_E R]LRN',
	    'PoTQ[Q RZT_T RTITS RTI[I[Q R_I_T RgVl[ R[W[[ RaVc[ RcJeOhRlS RUVTZ RUDgD_H RaJjJiMeQaS',
	    'Po\\DkD R[OlO R]ZjZ R]S][ R]IiIiO R]SjSj[ RWCWS[W R[HYM RVSS[ RSIRO RbD`O RWNUW',
	    'Po^KjK R\\YlY R\\E\\[ ReGeW R\\ElEl[ RVCVSZW RbO^S RdLiQjS RYHXM RdJaQ RSIRO RVMUX',
	    'Po^LkL R]ZlZ R]Q][ R^D^L R^DkDkL R]QlQl[ RWCWS[W R[HYM RWSSZ RSISO RWMVW',
	    'Po^ZiZ R^S^[ R^SjSj[ RWCVS[W RcHdK RdDhIlJ R^NiMdR RdD]J R[HYM RVSR[ RSIRO RVMUX',
	    'PocTlT RcYlY R^N^[ RcNc[ RcNlNl[ RWCWS[W R`I\\M RaGjGgL RWSSZ RbC_K RTISO R[HXMVW',
	    'Po\\RlR R^I^R R]JiJiR RdJdTjZ RVCVS[X RaFhFdJ RaE\\J RYHXM RcT^YZ[ RSISO RVNUX',
	    'PobVgV Rg[l[ R\\H\\[ RcMcV RgSgV RcDlDl[ RVDVSYW R^D`H RbJfK RhNjT RZHXM RTISO R_O^T RVNUX',
	    'Po]JkJ R`WhW Rf[k[ R`Q`X R`QhQhW R]DkDkJ RWCVSZW RZHYM RVSR[ RSIRO R]NlNlUk[ RVMUX R]D]R\\Z',
	    'PoTEkE RVIiI RRLlL RYOkO RbUfU R[W[[ RfMfT R]P_S RhWl[ RUXR[ R[LUS RbWc[ R^EZO',
	    'Po]GkG R\\NlN R^ZjZ R^R^[ RdCdG R^RjRj[ RWDWS\\W RiHhM R[HYM R`HaM RTISO RWMVW',
	    'PoRG]G R_GlG RXCXP ReCeP RZJ\\L RXHZK RfIlN RhSeV RVJRN RWTTW ReG^N RXFUL R_QaVdYl[ R_Q]VZYR[',
	    'PoRH\\H RaHlH RRL\\L RaLlL Rg[l[ RRER[ RRE\\E\\L RaEaL RaElElZ RePdS RZPWS R^TgX R^S[WVY R_N^U',
	    'Po]HjH R]MjM R]SbS Re[l[ R]D]M R]O]Z RfOf[ R]DjDjM RlWk[ RWCWS[W R[HYM RkReT RTISO RcY[Z RWMUW',
	    'Po^IlI R^VkV R^ZkZ R^E^O R^Q^[ R^QkQk[ RlMlP RWCWS[W RhNkP R[HYM RVSS[ R^EeDgN RSIRO RdN]N RWMUW RjCcD',
	    'Po]FlF R^MiM R\\UlU R`[d[ R^I^M RdCdF RdTd[ R^IiIiM RWCWS[W R[HYM RWSSZ R^QiQdS RTIRO RWMVW',
	    'Po\\GkG R\\MlM R^UjU R^ZjZ R^P^[ R`C`M RgCgM R^PjPj[ RWDVS[W R[HYM RSHTO RWNUX',
	    'Po\\GlG R[TlT RdCdG RdQd[ RWDWS[W R_LbO RgLkP RgLcP R[HYM RWSSZ R`I]O RhIeO RTIRO RWMVW',
	    'PoWHjH RRMlM RRTlT RWFWT R[W[[ R\\G\\T RbGbT RgGgT RhWl[ RUXR[ RXERJ RbWc[',
	    'Po^QjQ R[ZlZ R\\G\\K RdQdZ R\\GlGlJ RWDWTZW RfJkO RbJ]O RcCdG RZHYM RVSS[ RTISO RWMUX',
	    'PoUI]I RUM]M RUEUQ RRE^E]T RgVkY RaVc[ RaFeNhRlS RUVS[ R`FkFhNcR_S R[V\\[ R[QRR R]PUQ R_PXQ',
	    'PoUFlF R^JlJ R[I[Q RhGhQ R`LbO R_CaE RcQhReW R\\GXL RXTTW R_UbXeZk[ R_U\\XYYS[ RUFULTQ',
	    'PogTjT Re[l[ R\\F\\J RfKf[ ReKjKkT R\\FkFkJ R^QaT RWCWS[W RcCdF R`V[[ RlXl[ RZHXM RVSS[ R_J[R RbQ^Y R^LcM`W RTISO RWMUW',
	    'PoWHkH RVLiL RVPiP RVTlT RWGWT R[W\\[ RaFaT RhWl[ RYDSL RbWc[ RVWTZ RbD`H',
	    'PoaOhO R^PeP R`[g[ R`T`[ RgXh[ RVCVTYW RhUlX RgMiQ RfEkJ R`F\\J RaL]O RaReT RbH`M R\\UZZ RYHYM RSIRO RVNUX',
	    'Po`HiH R`LiL R^SkS R^WkW Rf[k[ R]E]M R^PkPk[ R]ElElM RWCVSZW RVSS[ RSHSO RZHYN RWMUX R^P^U]Z',
	    'Po\\KbK RfKlK R\\RbR RfQlQ R_[b[ Rh[l[ R\\DbDbZ RfDlDl[ RVCWTYX RZHXM RSISO RVNUX R\\D\\R[Y RfDfTd[',
	    'Po^QjQ ReZjZ R\\F\\I R]MjMjQ R\\FlFlI RWDVSXUYX RcDeF RfIjJ RZHYM RVSS[ RbI]K R]Q]UlUjZ RSISO RVNUX',
	    'Po\\FlF Rh[l[ R]M][ RaCaI RgCgI R]MlMl[ RWCWSZW ReRiV RbR^V R[HYM RWSSZ RdMaU RTISO R_JdJdObS RWMVW',
	    'Po^HkH R^MkM Rf[j[ R^D^M R^DkDkM RWCWS[W R`O[T RbS\\Y RfVa[ RhQdY RVSS[ RSHTN R[HYM R^RlQk[ RWMUX',
	    'Poe[k[ RSNSQ RYQeQeZ RSNkNkQ RkWk[ RjEhG RUETH R]E[G RaE`H RXH]J RdHlK RdI^L RYWVYR[ RWJRL RYCWJ ReCdI RYQXX',
	    'Po\\LaL`[ RVDVTYW RhSk[ RgIgSc[ RgOjY RgOeY ReIkIjO RSISO RYHXM ReDcO RgLiW RVNUX RaC[F[SZZ RgMfW',
	    'PodFlF R]NjN R]RjR R[VlV R]J]R RdCdJ RdRd[ R]JjJjR RWCWSZW R[HYM RVSS[ RTISO RWMUX',
	    'PofMjM R^UcU RgUlU R^ZlZ R^P^[ RfPlPl[ RWCWS[W R[HYM RWSSZ RbC^L RaFlFjM RTISO RdN^P RWMVW',
	    'PoeSgXj[ RZV]Y RXU[W RhEdI RWWS[ R]Q\\T RaQ_T RkQhT RYFUJ RUQSU R^IiN RcVaZ][ R_C^J[MTN ReOgW RYOWX ReOcW',
	    'Po[GkG R^L^[ ReTk[ ReNgQ RhTaZ RVCVSXX RbNfW RcCdG RcLkLfV RZHXM R_I[P RdI`S RSISO RVNUX',
	    'Po_IlI R_NkN R_SkS R_YlY R`G`[ RfHfY RWCWS[W ReDfG R[HYM RVSS[ RaD]L RSIRO RWMUW',
	    'Po\\GlG R]LiL R^RiR R^ViV Rf[i[ R^O^[ R^OiOi[ RWCWS[W RgIkM RcCdG RcG]L RSIRO R[HXMUW',
	    'Po`LjL RaViV Rg[l[ R]E][ RaOaV ReLeV RiOiV R]ElEl[ RWCWS[W RaFbJ R[HYM RgFgL RSISO RWMUW',
	    'Po_JkJ R\\W\\[ RhWkZ RhDkG RbWc[ RVH\\K RTLYN RVWTZ RXCVHRL RWF_F[NUT ReDeLcP^T R ReKgPkS',
	    'Po]KjK R]PjP R\\UlU R]F]P RfQf[ R]FjFjP RWDWS[W RbCaF RZHYM RVSS[ RdFcPaR]S RTISO RWNUW',
	    'PoTI]I RaIjI RTN]N RaNjN RTETN RTE]E]N RaEaN RaEjEjN RiReU RXSUV R_PaVeYl[ R_Q]VZYR[',
	    'Po_KjK R_PiP RaViV Rg[l[ R]D][ RaPaV R]DlDl[ RWCWS[W RdMdO RbGbJ R[HYM RVSS[ RgGeK RTISO RWMUW',
	    'Po]IjI R]MjM R\\TlT R]D]M RhOh[ R]DjDjM RVDVSYW R`P`W\\[ RZHXM RcO\\Q RRHSO RVNUX',
	    'Po_IkI R_MkM R^UjU R^ZlZ R^Q^[ R_E_M R_EkEkM R^QlQl[ RWDWS[W R[HZM RSHTO RWMUW',
	    'Po\\FkF R[MlM R[RlR Rg[l[ RdCdJ RgRg[ RhIhR RlXl[ R[HYM R_Y[[ RTIRO R_I`R_Y RWDWSS[ RWSZW',
	    'Po`HjH RTI\\I R`MjM RTN\\N RfQjQ RTETP RTE\\E\\N R`DjDjQ RhSeW RXSUW R_ScXgZl[ R_Q]V[XXZR[ R`D`J_O',
	    'Po`[h[ RaTa[ RgXh[ RcSeW RWCWSZW RiUkZ RbJeL RcEjL RbF\\M R^NiNfS R[HYM RVSS[ RcCaH R]U\\Z RSIRO RWMUW',
	    'Po]FlF R^NlN R_SjS R_JjJjS RfVk[ RVCVSYW RdCdF RcU^[ ReHdSgX RYHXM RSISO RVNTX R]F]R[Z RdNcV',
	    'Po]JlJ R`RkR R_ZlZ R_U_Z R`M`R RfLfZ RlMlR R]DlDlJ RlUl[ RWDWTYX RYHYM RVSR[ RTISO RVNUX R]D]R[Z',
	    'Po\\FlF R_JiJ R_MiM R[QlQ RfVkV R]ZkZ R]Q][ R_C_Q RgQgV RiCiQ RWDWS[W RZHXM RVSS[ RTISO RbQaV RWNUW',
	    'Po]GlG RaJhJ R_SjS R\\VlV R_N_S RaDaJ RdJd[ RiDiJ R_NjNjS RWCWSZW RVSS[ RSHSO R[HZN RWMUW',
	    'Po[GlG R^MiM R\\QkQ R]VjV RZZlZ RcJcZ RVCVSZW ReGkM RaG[M RYHXM RbC_J RSISO RVNTX',
	    'Po\\HlH R]OjO R]SjS R]WkW R[[l[ R]L]S RdDd[ R]LjLjS RVCVSXUYX RVSR[ RSHSO RYHXM RVNUX RjC_E',
	    'Po\\JaJ R\\PaP R\\E\\[ RdDdK RdDiDiMlLlM RfUk[ RVCVTYW RdPgX RZHXM RVSR[ RcOkOhVdY`[ RaD\\F RTISO RbU[V RVNUX',
	    'Po[LbL RdLjL R[QbQ R[VbV R[D[[ R[DbDbL RdDjDjL RfVl[ RVCVTYW RiUc[ RdQhX RcPkPgW RZHXM RSIRO RVNUX',
	    'Po]IkI R]PjP R]TjT R[WlW R\\E\\H R]L]T RdGd[ R]LjLjT R\\DkEkH RVDVSXUYX RYHXM RVSR[ RSISO RVNUX',
	    'Po^SfS R^YdY R^L^S RbHbY R^LfLfS RlWl[ RVDVTYW RZHXM RSISO R\\EiEiVj[ RVNUX R\\E\\SZZ RfH^H RfVgZaY',
	    'Po\\GlG R]RkR R]K]R RdCd[ R]KkKkR RWDWS[W RcRlZ RdR\\Z R`M`P RgMfP R[HYM RWSSZ RTISO RWMVW',
	    'Po_JiJ R_RjR R_WjW R\\ZlZ R]F]J R_N_W RdCdG R_NjNjW R]FlFlJ RWCWS\\W RZHYM RWSSZ RTISO RWMVW',
	    'Po]HjH R]MjM R]RkR R]UjU R[[l[ R]D]L RcNcZ R]DjDjL RWDWSZV RZHYM RWSS[ R^N\\T RTISO RWMUW',
	    'Po]JjJ R]NjN R[RkR R]VjV R[ZlZ R]F]N RcRcZ R]FjFjN RVCVSZW RZHYM RbCbF RSISO RVNUX',
	    'PoZKbK R_D_[ RVCVSXX RYHXM RcHdO RgCgPjZ RkHjO R^J[T RgNdZ RSHSO RbDZE RgKiW RVNUX RgKeW',
	    'PoRGlG RUJUU RbJbR RiIiU RdUhVl[ RWL[M RWP[Q RXDZF RUXR[ RfCcG R[W\\[ RbWc[ RUJ]J]UYU',
	    'Po\\ElE R^MjM R]UkU R]ZkZ R]P][ R^H^M RdPdZ R^HjHjM R]PkPk[ RWCWSYV RVSS[ RSHSN RZHYM RWNUW',
	    'Po\\FlF R[SlS R]L]S R`C`I RgCgI R]LjLjS RWCWS[W RcIcSeWhYl[ RbU_Y[[ RTISO R[HXMVW RcOcV',
	    'PoVFdF RRIkI RWOhO RWRhR RWLWR R]C]I RVMhLhR RXUTX RhTeW RbJeK R_R`VcYk[ RgE]JRM R_R]V[YS[',
	    'Po\\JlJ R^QiQ R^TkT R[XlX R^L^Q ReQe[ R^LjLjQ RWCWSZV RZHXM RWSS[ R]FiFiJ RTIRO RbCaJ R^T^X RWMUW',
	    'Po]JjJ R[MlM R\\R\\[ R]E]J RaRa[ RcCcJ RfRf[ RkEkJ RWDWSYV RZIYM RVSS[ RdMbR RTISO R\\RkRk[h[ RWOUX',
	    'PoeIlI R`ZkZ R`Q`[ ReCeQ R`QkQk[ RXJ]L RTFUI RXT]Y RWVS[ R^Q[T RXJTN R]E\\I RTQUU RYCWK RYOWX',
	    'PoTE_E RaKkK RTS_S RbSkS RTETS RWHWO RbEbS RaEkEkK RgVl[ RkPkS R[V\\[ RaVc[ RUVSZ RZOZS RZEZI^I]NZN',
	    'Po^KjK R[OlO R`SjS RcFdI RWCVSZW RhFlI R^WZ[ R`SeXk[ RZHYM RVSS[ R^E\\J R`R\\Y RiTeY_[ RTIRO RaK_V RWMUW RhD^E',
	    'Po[EkE R[TkT R\\I\\Q RaIaP RfIfP RWDWSXUZX RcSdWgYl[ RZHYM RcEaJ RbV_Y[[ RTISO RcQaX R\\IjIjPhP RWNUW',
	    'Po\\HlH R^LdL R_UcU R_O_W R_OcOcU RVDVTYW RhDkF RgUa[ RjMeX RZHXM RVTR[ RRHSO RlWj[k[ RgCgKgOhZ RVNUX R\\H\\SZ[',
	    'Po[ElE R\\OkO R\\TkT RZZlZ R\\I\\O RaEaO RcQcZ RfEfO R\\IkIkO RYHXM RSISO RVDVQR[ RVQYV',
	    'Po^ElE RSJ\\J R`LjL RSO\\O R^PlP RSESO RSE\\E\\O R`E`P RiReV RXSTW R`HjHjL R_QaVdYl[ R_R]VZYR[',
	    'PoTH]H RaHjH RTK]K RaKjK RaNjN RTN]N RTDTN RTD]D]N RaEaN RaEjEjN RiReV RXSUV R_QaUcXgZl[ R_Q]VZYR[',
	    'Po]IjI R]MjM R\\RlR RZZlZ R]D]M RcMdR R]DjDjM RWCWS[W RiSfY R[HYM RVSS[ R_S`Y RTIRO RWMUW',
	    'Po^JlJ RaRlR RaVlV RaZlZ RaMa[ ReDeJ RaMlMl[ R^DlDlJ RWDWS[W RWSSZ RTISO R[HXMVW R^D^R\\Z',
	    'PoRO`O RTT]T R[W\\[ RUJ]J]T RhWlZ RdNjT RVWTZ RVERJ RbWc[ RbHeP RVF[FYJ RcC`K RbHkHgOcS_T',
	    'Po]HlH R]LlL Rg[j[ R]D]L RdDdL R^OjOk[ R]DlDlL RWCVSZW RZHXM RVSS[ RaRhS RRHSO RVMUX R^O^V]Z RjU`W',
	    'Po\\GlG RcLhL R^OlO R\\SlS R^D^P RcCcL RdOd[ RhDhL RWDWS[W RdSiYlZ R[IYM RVSS[ RcS_X[Z RTISO RWMUW',
	    'Po`JlJ RaMjM RaQkQ R`UlU Rc[l[ ReCeY RaFjFjM RVDWTYW R]WY[ RYHXM R^R[Z RSISP RZF_F[N_N]W R[Q]V`Zh[ RVNUX',
	    'PoSUdU RSLSP R\\P\\[ RlXk[ RjEhG RUESG R]E[G RbE_G RXH]J RdHkK ReCdH_J RWIRK RYCWI RSLjLkP RVPfPgY',
	    'PoaOeO R\\SlS RdPd[ RfHfN RVCVSZW RdRkZ R]IkIjL RdR]Y RaFgI RZHXM RVSR[ R^DiEdG RdIaL\\N RTISN RVNUX',
	    'Po\\FlF R_KiK R_OiO R\\SlS R_C_O RdOd[ RiCiO RWCVSZW RdSkZ RdS]Z RZHYM RVSR[ RTISO RVMUX',
	    'PoeNjN R[TlT R^H^R RfIfN R]JjJjR RWDWS[W RcSeVhYl[ RZHXM RaC]J R`FfFdI RbV`Y[[ RTISO RbIaO RcQbX RWMUW',
	    'PoSKZK R_PfP RSRZR ReUiU RSEST RSEZEZR R[W[[ R_J_R R_JfJfP RhVlZ R_F\\J RUVSZ RbWc[ R`C^I R_GkGkNiT',
	    'PofKjK RTK[K RTS[S R^TjT RTETT RTE[E[S R\\W\\[ R^M^T R^MjMjT RhVkZ RUXR[ RbVc[ RcEbH`J]K R]EkEjK',
	    'Po]IjI R]NjN R[QlQ R]D]M R^Q^Z RcDcM R]DjDjM RWCWS[X RcQeVhYl[ RZHXM RjTeV RTISO RWMUW ReY[[',
	    'Po]DlD R^MjM R^RjR R^VjV R^I^V RcDcI R^IjIjV RiXk[ RgXjY RWDWS[W R[HYM RWSTZ R`Y[[ RTISN RWMVW',
	    'Po\\HlH RfRkR R]VkV R]ZkZ R]M][ RbHbQ RgGgR R]MkMk[ R_D`F RhEfI RVDVSXX RYHXM RVSR[ RTISO RVNUX',
	    'Po]HlH R`[d[ RfQfY RWCWSZV R`DaG RgNkW R[HYM RVSS[ RgDeH RcH`K\\M RcNaP\\R RiKeP`V[X RdNeV RTISO RWMUW',
	    'Po^HiH R^LiL R[OlO Re[i[ R^D^L R^DiDiL RWCWS[W R_Q[U RbT\\Z ReVa[ RfSdY RZHYM RTISO R]SjSi[ RWNUW',
	    'PoSKaK ReTjT R\\W\\[ RdFdP RjDjT RSGVI RVQZR RhWl[ RYG\\I R^FaI RUXR[ RUDSF R[DYF R`D^F RYKTP RbWc[ RVN`N]QYSSU',
	    'PoUFeF RSJlJ RXQhQ RXUhU RYMYU R\\W\\[ R^C^J RVNhNhU RhWl[ RVXS[ RbWd[ RhF]LRO',
	    'Po]HcH ReNlN R]VjV R]ZjZ R]C]N R]Q][ ReDeM R]QjQj[ RlKlN RVCVSZW RbNbQ RZHXM RkFeI RSISO RbM\\N RVNTX',
	    'Po_LjL R\\YcY ReYlY R\\P\\[ R_E_L R\\PcPc[ RePe[ R_EjEjL RePlPl[ RWDWS[W R[HYM RSHTO RWMUW',
	    'PoaWhW Rh[l[ R\\N\\[ R_D_N RaRaW R_IeIeN RaRhRhW R_DjDjN R\\NlNl[ RWCWSZW R[HYM RVSS[ RSIRO RWNUW',
	    'Po\\HiH RYLlL RWFWR R\\D\\H RbHbR RhSeW RWERJ RYSUW RdMlP RaL_NYQ R_TcXfZl[ R\\DiDiH R_TZYR[',
	    'Po[IcI RfKlK ReRlR R\\XbX Rh[l[ R\\P\\Z R_C_P R\\PbPbX RfDlDl[ RVDVSXW RYHXM RSIRO RVNTY RfDfRdX',
	    'Po^IiI R^NiN RZZlZ R]Q]Z R^E^M RaQaZ RfQfZ R^EiEiM R]QjQjZ RWDWS[W R[IYM RVSS[ RTIRO RWNUW',
	    'Po\\GlG R`QiQ R_UiU Rh[k[ R]K][ RdDdK RdQdY R]KkKk[ RWCWS[W RaMaO RgMeQ R[HYM RVSS[ RSIRO RWMUW',
	    'Po^IjI R^MjM R[PlP RdUjU RcZlZ R^E^L RdPdZ R^EjEjL RWCWSZW R[HYM RVSS[ R^S[Z RSIRO RaYhZ RWMUW',
	    'Po\\FlF R^MiM Rg[l[ R\\P\\S R^I^M R`SgSg[ R^IjIjM R\\PkPkS RlXl[ RWCWSZW RcCdF R[HYM RVSS[ R^Y[[ R`S_Y RTISN RWMUW',
	    'Po^KlK R_UlU R^F_R_[ RdOdZ RhOhZ R^FlFlK RVCVTYW RdCeF RYHYM RVSR[ RTISO R_OlOl[j[ RVMUX R^N\\Z',
	    'Po]FlF R]IkI R\\MlM R\\SlS RZWlW Rf[j[ R^P^[ RaCaL RdMdW RhChL R^PjPj[ RWDWS[W R[IYM RVSS[ RTIRO RWMUW',
	    'PoaIjI RaMjM Ra[l[ RaDaW RaDjDjM RWCVTZW RePjV R[M^M]XZZ RjPhR R\\E]I RZHXM RVSR[ RfU`V RTISO RVMUX',
	    'Po]GlG R^NlN R_QjQ R_ZjZ R_T_[ ReHeT R_JjJjQ R_TjTk[ RVCVTYW RcDfF RYHXM RSIRO RVMUX R]G]Q[Y',
	    'Po_LhL RgOkO R_XhX Rf[j[ R_T_W R_H_L RhHhL RhThW RWDVSZW RaP\\T RZHXM R`C^H RbM]O RbY][ RcHcM RRHSO RcScY R_FlFkN R_RlRk[ RVNUX',
	    'Po]MlM Ra[d[ Ri[l[ R]PdPd[ R]HlHlL RfPlPl[ RWCVTZW RcP_T RdT_X RkPgT RlThX RVSS[ RTIRO RZHWMUX R]E]R\\Y RcD]E RjC`E',
	    'Po_IhI R]QjQ R_ZhZ R]E]Q R`T`Z RhWhZ R]EjEjQ RdSeV RWCWSYV RcJgN RiSlX R[HYM RVSS[ RaN_N RSISO R\\T[Y RcGbM RWMUW',
	    'Po[GlG R\\MjM R\\PjP RZTlT Rc[g[ R\\J\\Q RcCcQ RgQg[ R\\JjJjQ R^VaY RVDVTYW RYHXM RfDjE RTISO RVNUX',
	    'Po]HjH R]LjL R[SlS R]D]L RcLcS R]DjDjL RfSf[lZl[ R]N_Q RWDVSZW RiNgQ RZHYM RVSS[ R_YZ[ RTIRO RaS_Y RWNUX',
	    'PofHjH Re[j[ R^QjQj[ R\\EjEiH RVCVSXW RYHXM R_I]N RdCcF RhIfNcN RaSgT RbG\\H R[KbKbN_N RdKlKkNhN RRHSO RjV`W RVMUX R^Q^V][',
	    'Po^GlG R_JjJ R[TfT RaOa[ RlXl[ ReVfZ RVDVTZW R^W[Y ReOdR R]O^S RZHXM RVSR[ R_C\\J RRHSO R[MiMiVj[ RVNUX',
	    'PoaEjE RfQiQ RWQYQ RYJYQ RhTeW RSD]DYH RYJSO RcC_G RXTTW RVF[H RRJ]J\\M RiF`J RdLaP]Q RaEdHlJ R_LjLiQ R_U[YT[ R_UcYl[',
	    'Po^JiJ R^MiM R^QiQ R_[h[ R^F^Q R`U`[ RcCbF RhXh[ R^FiFiQ RWDWSZV RiUlZ RaSeT R[HYM RVSS[ R\\U[Z RTIRO RWNUW',
	    'Po^NjN R[ZlZ R]Q]Z R^D^M RbQbZ RfQfZ R^DjDjM R]QjQjZ RWDWS[W RcHgK R[IYM RVSS[ RdFbK RTIRO RWMUW',
	    'Po^GjG R\\KlK R\\PjP R[ZlZ R]S]Z RbSbZ RdCdK RfSfZ R]SjSjZ RWCWS[W RgMkQ RbK]P R[HYM RVSS[ RSISO RWNUW',
	    'Po\\FlF R_MiM RaXhX Rh[k[ R]P][ R_I_M RaSaY RdCdF RaShShX R_IiIiM R]PlPl[ RWDWS[W R[HYM RWSSZ RTISO RWMVW',
	    'PoXDaD RdLkL RXOaO RXTbT RdTlT RXDXT R[W\\[ R]D]J R]O]T RXJaJaO RdDdT RdDkDkK RlQkT RhVlZ RUXR[ RbWc[ RUDUNSS',
	    'Po^IjI R]OjO R]SjS R]WjW R]L]V R^D^I R^DjDjI R]LjLjV RVDVTZW RfXl[ RYHXM RVSR[ RaXZ[ RTIRO RVNUX',
	    'PoRH^H RbKkK RTM]M RTQ]Q RYU]U RbUlU RTJTU R\\W\\[ RTJ]J]T RbCbK RbMbT RhWl[ R[E_H RUXR[ RbWc[ RWCUH RkIkK RkRlU RjEbG RjNbP',
	    'PoSH^H RaJkJ RTM]M RTP]P RaSkS RZS]S RTJTS RbCbJ RbLbR RkGkJ RkPkR R[E_H RhTeW RXCSH RXTTX R_S`VcYk[ RiEaG RiMaO RTJ]J^S]V[YS[',
	    'Po\\HbH RfHlH R\\LbL RfLlL Rh[l[ R\\D\\[ R\\DbDbL RfDfL RfDlDl[ ReTiX RWDVSZW RdOgV RVSS[ RdPaW_W RTIRO RZHWMUX',
	    'Po`PiP R^YkY RTITO R]F]J R^S^[ R`J`O R`JiJiO R^SkSk[ R]FlFlJ RWCWS[W RcCdF R[HYM RWMVW',
	    'Po[FbF R^MkM R[RlR R\\ZjZ R\\U\\Z RcMcZ RjTj[ RVCVTYW R]G^J R^K\\O RcGcJ RjFgM RZHXM RVSR[ RSIRO RVNUX RjD`E',
	    'PoRHlH RTPiP RUSiS RRVlV RTJTP R[Y[[ RTEaE_V RTJiJiP RiXl[ RYL[N RUXR[ RdLcO RbYb[ RdD_E RhDbD',
	    'Po^MiM R^PiP R[TlT R\\E\\H R^I^P R^IiIiP R\\ElElH RhWk[ RVDVSXW RdRdT RYHXM RVTR[ RSISO R`W\\ZZ[ RVNUX',
	    'Po[HkH R[OlO R\\SjS RaHa[ RfGf[ R\\KjKjS RVDVTZW ReSlZ R^D`F R_U[Z RgDeI RYHXM RVSR[ R`R^W RSISO RVNUX',
	    'PoSMSQ RSMkMkQ RXH\\J RkEhG RhQdU RUETH R]E[G RaE`H RXRUU R_PaUdXj[ ReHlK RdI_K RWIRK RYCXI ReCdI R_O]UZYS[',
	    'Po^KiK R^OlO Re[j[ R_V`Y R^FiFiK RWDVSYW RfUhW RcCbF RcUdX RZHYM RVSR[ R\\VZZ RTIRO R^F^SkSj[ RVNUX',
	    'Po_ZiZ R]G]J R_S_[ RdCdG R_SiSi[ R]GkGkJ RWDWS[W RdMiRlS RgJkN RcM]T RaJ]N R[HYM RVSS[ RTISO RWMUW',
	    'Po^DjD R]HlH R^OjO R^SjS R^VjV R^L^V RdDdH R^LjLjV RiYk[ RgXjY RWDWS[W R[HYM RWSSZ RbX[[ RTIRO RWMVW',
	    'Po^TcT RfTkT R^ZkZ R^O^[ RdGdK RfOkOk[ RWDWS[W RiFlK R[HYM RVSS[ R`E]L RdM^O RTISO RWNUW RiC_E',
	    'Po^OiO R^SiS R^ZjZ R^L^[ R^LiLiR R]UjUj[ RVCVSZW RbIdJ RcDkL RcE\\L RZIXM RSISO R^O\\Y RVNUX',
	    'PohNkN R]UkU R]ZkZ R\\E\\N R]Q][ RdQdZ R]QkQk[ RdElElM RVDVTYW RYHYM RVSR[ RaHbK[N RdMcN RSIRO RbD\\E RgEfL RVMUX',
	    'PoTE_E RaKkK RVO]O RTS_S RbSkS RTETS RVHVO RZOZS RVH]H]O RbEbS RaEkEkK RgVl[ RkPlS R[V\\[ RaVc[ RVVTZ RZEZH',
	    'Po\\HkH R\\LkL RZPlP R[TkT R[WlW R\\D\\L R_M_S RcDc[ RhMhS R\\DkDkL RVDVTZW RZHXM RVSR[ RTIRO RVNUX',
	    'Po\\ElE R]MkM R^PjP R\\TlT Ra[d[ R]H]M RbEbM RdTd[ RfEfM R]HkHkM RWDWS[W RhVlZ R`V\\Z R[IYM RVSSZ RTIRO RWMUW',
	    'Po]QjQ R`ZhZ RTITO R]F]Q R`T`Z R]FjFjQ RcSfV RWCVSZW RjTkY RbLgM RhWiZ RbCbF RcH`K RZHYM RVSS[ R]U\\Y RbIgJdN`O RWMUX',
	    'Po\\FlF R\\OkO R_TdT Rf[l[ R_Q_[ RaFaO RfQf[ RfFfO R\\JkJkO RlXl[ RVCVSZW RcCdF RZHXM RkSfU RdZ^Z RTISN RVNUX R\\F\\SZ[',
	    'Po[DbD R[MbM R[QcQ R\\UjU RZZlZ R[D[Q R_M_Q R[IbIbL RcRcZ RhMlQ RiMdQ RVDVSXW ReFiO RdEkFgO RYHXM R_E_I RTISO RVNUX',
	    'PoRF_F RTK]K RRQ_Q RUUYU RTHTK RYCYF RYOYU R[W[[ RTH]H]K R`HiHjS RlQlU RhWk[ RdCcMfQ RUXR[ RbP^U RaWc[ R`LcM RSN^NYP RdHcP',
	    'Po^UlU R^ZlZ R^Q^[ R]DcDcN R^QlQl[ RWDWS[X RjIfM R`F]H RaJ]M RiFfH R[HYM RWSSZ RTISO RaNcNaN ReDlDlNiN RWMVW',
	    'Po[HlH R]KjK R\\QjQ R[TlT R\\N\\Q R]E]K RcCcQ R]EjEjK R\\NjNjQ RVCVSZW RZHXM RaQ_V R^Xk[ RTISO RhUdY[[ RVNUX',
	    'Po`PkP RaSjS R`VkV Ra[l[ ReMeY R[K]M RYQ]Q]X`Z RVDVTYW R[D^G R]WYZ RcE_I RbGfKlN RYGXL RbGiGdL_N RTISO RVNUX',
	    'Po]IkI R]OlO R]RkR R]VkV R]ZlZ R]D]I R^M^[ RdCdI ReNeZ RkDkI RVDVSXX RdKeN RYHXM RVSR[ R^K[Q RTISO RVNUX',
	    'Po\\QkQ RZZlZ R\\DbDbM RcOcQ RdDkDkM RWDWS[W RhFeI RiJfM R_F]I R`J\\N R_S`X RiSgX RZHYM RWRS[ RTISO RWMUW',
	    'PoaNkN RaSkS RiXkX Rb[l[ RaJaX RfJfX RaJkJkX RVDVTYW RZE^I RdGhJ RiFeH RaEkDhG RYN]N]WYZ R]Xc[ RSISO RYHXM RVNUX',
	    'Po[OkO R]SjS R]VjV R]YlY R^N^[ RcCcJ ReMeY RVDVTZW RhElJ RYHXM RVSR[ R^E\\J RfIdK[O RSIRO RVNUX',
	    'PocMjM RaQkQ RWJWR RcKcQ R^FaH RhTeV RVFRG RWDTG RXHTL R^FZJ RfEbJ RXTUW RfFjH R^D\\I R^K[Q R_TbXfYl[ R_T\\XXYR[ R_QgQ',
	    'PoTG^G RaGlG RWJ^J RUL`L ReQhQ RTD^D^G RhChQ R^M_O RbJeM RiSfW RWNUP RXSTW R_R`VdYk[ RZLZQXQ R_Q]VZYR[ RTDTJSP',
	    'Po\\JkJ R_PjP R^VkV R_[l[ R_S_Z R_M_P RdSdV R_MjMjP R\\FkFkJ R^SkSkV RVDVTYW RcCdF RTISO RYHXM RVNUX R\\F\\QZY',
	    'PoeGkG R[KaK R_E_[ RkHfM RhNbT RkSeY RgDbI RVDVSXX R^K`R RfPlPiU RXHXM ReIgLbN ReTgW`[ R`D[E R^J[T RSIRO RVNTX',
	    'Po]GlG R_MlM RbSiS RbIbS RcVcZ RiIiS RVCVSYW RfVhZ RjUlZ RdCeF RYHXM R_V^[ RVQTZ RSIRO RVNUX R]G]R[Y',
	    'PoSE^E RRI_I RRL_L RXT\\T R[W[[ RhWl[ RUXR[ RaWc[ RaHdOhSlU RcC`K RTTRT RbHkHgOcS_T RYCXIXLUS RWO]O\\T',
	    'PoSE]E R`EkE R`GkG RTG]G RSJ^J R_JlJ RVOiO RUQiQ RXCXK ReCeK RULiLiQ RiTfX RXUTX R_TcYk[ R_S]V[YS[',
	    'PodDkDkM RWDWS[W RgFeH RhIeL R_F\\H R`I\\M RdNlR RZHYM RVSS[ R[DbDbMaP[R RcR^T RfT_W RTIRO RiVdY][ RWNUW',
	    'Po[FlF R`IgI R]PjP R]SkS R[VlV R]L]P R`C`I RgCgI R]LjLjP RWDWSZW RcIcVfYk[ R[HYM RVSSZ RcV`Y[[ RTIRO RWMUW',
	    'PoZElE RbMhM R_VdV RfVkV R][l[ R]E]Z R_P_V RbHbM R_PdPdV RfPfV RbHhHhM RfPkPkV RVDVTYW RZHXM RVSR[ RTISO RVNUX',
	    'PoTF]F RRI^I R[M^M RSP^P RRTYT RiUlU R[W[[ R`HiHiT RhWk[ RdCdMfP RUXR[ RVKSN RaWc[ R`LdN RXCYI[M RlPlU R`T^TXT RdFbQ RXMZTUT',
	    'Po_ElE RTL\\L R`MjM R`QjQ R`VjV RTCTL R\\I\\L R`I`V ReEdI R`IjIkV RhXk[ RXU\\W R]Q[S RWWS[ RbY^[ RUPST R\\ETG RXOWX',
	    'Po^GjG R^JjJ R\\QkQ R\\M\\Q R^D^J R^DjDjJ R\\MkMkQ RVCVTYW RZHXM RVSR[ RaMaQ RfMfQ R^SbXfYl[ RTIRN R\\TjTgWcY[[ RVNUX',
	    'Po\\GjG R]MiM R]QiQ R]UbU Rf[l[ R\\D\\G R]S][ R]J]Q RdCdG RfSf[ R]JjJjQ RjDjG RlXl[ RVCVTZW RVSR[ RkTfV RbZ[Z RSISO RZHWMUX',
	    'Po[FlF R`WgW Rh[k[ R\\L\\[ R`R`Y R`RgRgW R\\LkLk[ RWCWSZW ReNiQ RbDeF RbN^Q R_H`K RVSS[ RgHfL RTISO RZHXMUX',
	    'Po]GiG R]KiK RZNlN R\\QbQ R\\UbU R\\N\\X R]D]K RbNb[ R]DiDiK RgVl[ RVDVSYV RiVd[ ReQhX RdQkQhX RYHXM RVSR[ RTISO RVNUX RcWZX',
	    'Po]OlO R_VkV R_ZkZ R]G]O R_R_[ ReGeO R_RkRk[ R]GlGlO RWCWS[W R`CaF RiIhM RiCfG R`IbM R[HYM RVSSZ RSIRO RWMVW',
	    'Po[DbD RdDkD RdJlJ R]VjV R]ZjZ R]R][ RdFdJ R]RjRj[ RiLiPlOlP RgLcP R^L[P RVCVSXW RYHXM RVSR[ R[F[JaJaN_O RSISO RVNUX R`D_L RhDgL',
	    'PoSEkE RUHiH RVMhM RRQlQ RVViV RVSVV RVJVN R\\X\\[ R_C_H RVJhJhN RVSiSiW RiXl[ RUXS[ RZN[P RbXb[',
	    'Po[FlF R]IjI R[SlS R]ZiZ R]U][ RcCcI R^KiKiO R]UiUi[ RWCWS[W R`P`R R^OhOfS RZHXM RVSS[ R^K^O RTISO RWNUW',
	    'Po`HgH R^NiN R_[b[ Rh[k[ R^J^N R[QbQb[ R^JiJiN RdQkQk[ R\\R_U ReShU RVCVSXW RdElH RVSR[ RbV[Y RkUdY RcC`G[I RTISO RYHXM RVNUX',
	    'Po\\HjH R\\LjL R[QkQ RZUlU R\\D\\L R`N`U RcDcL RfNfU R\\DjDjL RgXj[ RVCVSYW RYGXM R_YZ[ RRHTN RVNTX',
	    'Po\\HlH R]KjK R]TiUkV R]M]WaVa[ RdCd[ RfVf[ R]EjEjK RkMk[ RVDVSYW RZHXM RVSR[ RfMfQkQ RRHSO RVNUX RaMaQ]Q\\Z',
	    'Po[GdG RZMlM R\\TcT R\\XcX Ri[k[ R\\P\\Z R]H]K R\\PcPcW RVDVTYW RhUb[ RiEkI RjOgW RlWk[ RYHXM RbHaK R_C_G RSISO RgCgMgPhZ RVNUX',
	    'Po[FlF R_IgI R[LlL R]SjS R]VjV R]O]V R_C_I RcLcV RgCgI R]OjOjV RVDVTYW RfXl[ RYHXM RVSR[ R`XZ[ RTISO RVNUX',
	    'Po\\IbI ReIkI R]PjP R]SjS R[WlW R\\D\\I R]L]S R\\DbDbI RcLc[ ReDeI R]LjLjS ReDkDkI RVDVSYX RYHXM RVSR[ RTIRO RVNUX',
	    'Po[FlF R\\JkJ R[NlN R\\SkS R\\WkW R_J_S R`C`H RcJc[ RgCgH RhJhS RWDWS[W R[HYM RVSS[ RTISO RWMUW',
	    'Po[DlD R\\KkK R]QjQ R]TjT R[WlW R\\G\\K R]N]T RaDaK RcTc[ RfDfK R]NjNjT R\\GkGkK RVCVTYW RYHXM RSISO RVMUX',
	    'PocIlI R`Wa[ RdVf[ RiVk[ RiDjG R[L`N RYHXL R^EZN R\\VZ[ RSIRO RVDVQSY RVRYV R]GbG_N[S RgDgLePaT RgLiPkS R\\IaJ',
	    'PoZT]T RTY]Y RTPT[ RYO^O^Z RfVk[ ReIgJkN RdJaN RjEiI RdV`[ RkQiU ReShY ReScY RWDSL RbF`I RaQ`U RXNTP ReOfV RVF]F\\LYL RWTTTXT ReCdK',
	    'PoSG]G RRK^K RSS]S RXCXK RXPXX RfVjZ R[M^P RdJhKkO RdK`O RkFhH RkQhT RVMRP ReSgY RaF`I RaQ`U RdWaZ^[ ReCdL ReOdW R]WRY',
	    'Po\\HkH R`OgO RaXgX R]Q][ R`J`O RaTaY RaTgTgX R_KhKhO RVDVTYW RbG]L RfHhKlL RZHXM RVSR[ R]EcEaJ R]QjQj[g[ RRHSO RVNUX RiCbD',
	    'Po[GlG RfNkN R\\OkO R\\RkR R[UlU Rb[g[ R\\J\\R RfFfM RgRg[ R\\JkJkR R^W`Y R_DaE RaGaL_N RhDeG RVDVSXW RYHXM RVSR[ RTISO RVNUX',
	    'Po_KgK R^TiT R[[k[ R^O^T R^OiOiT RWDWS[W ReGkM RkGgI R]G_IZN RhDeG R`V`Y RdCfI RgUeY RZHXL RVSS[ R\\EbD_J RTIRN RWNUX',
	    'Po[GcG R\\NbN R]VcV RZVaV R\\I\\M R_C_G R\\IbIbM RgVb[ RVDVSXX RgRkZ RiPeY RYHXM RVSR[ RfNiX RTISO R[QbQ_T_[\\[ RfJhW RfIkIhU RfCdO RVNUX',
	    'PoRUlU R`S`[ RaL`O RkMhO RYDUH RULTO R]M[O RdOlS RXP\\Q RhEdF RWQRS ReObQ_S R_UTZ R^GjJ R_UgYlZ RYKWQ ReKdQ R_C_F\\ITJ',
	    'Po^IkI R_LjL R_PjP R_TlT R_G_T RaWb[ ReFeT RWDWS[W ReWf[ RiVk[ R[HYM RVSS[ R`C\\M RfDeJ R^W\\[ RTIRO RWNUW',
	    'Po]FkF R\\JlJ R]PjP R]TjT R]WjW R]M]W R`G`L RdCdH RhGhL R]MjMjW RWDWS[W RgXl[ R[HYM RaX[[ RTISO RWMVW',
	    'PoZGcG R\\KaK R\\PaP RYUcU RXHWM R\\C\\T RaCaT ReLkLj[ R`WbX R\\WXZ RVCVSXX RSISO RUOTZ RkDeFeTcZ',
	    'Po\\GkG R_PiP R_TiT R`[d[ R_K_S RdTd[ R^MiLiS RVCVTZW RhVkZ R]I_K RfHlN RkIhK R_V[Z RaI\\N RVSR[ RcC`K RSIRO RZIWMUX',
	    'PobNeN RaYhY Rh[k[ R\\P\\[ RaUaZ RfGfN RaUhUhY R\\PlPl[ RfQiT RbQ_T R\\HlHiK RWCWTYX RaFhH RZHXM RVSS[ R^DjDeH\\M RSISO RVMUX',
	    'Po\\HlH RdRlR RaWlW RcCcN RdRdW RiNi[ R^D`G RiDgG R_V[[ RWDVSXW RcGgKlM R[T_V RYHXM RVSR[ R]O[U R\\RaQ^X RcG_L[M RTIRO RVMUX',
	    'Po\\FkF R[KlK R]QjQ R]TjT R\\WkW RZZlZ R]M]T RcMcZ R]MjMjT RgGfJ RVCVSXW R_G`J RcCdF RYHXM RVSR[ RSISO RVNUX',
	    'Po]FjF R\\IkI R\\MbM RdMkM RcQlQ R[TlT Rf[l[ R_K_Q RcCcI RfTf[ RgKgQ RlXl[ RWDWS[W RZHYM RVSS[ R`U^YZ[ RTISO RbQ[Q RWMUX',
	    'PoRG[G RcGlG RWCWP RgCgP RWHZK R\\K`L RhSeV RUKRN RaD\\I RaJ\\O RWTTW RgFjM R\\EaG RWFTM RgFcN R_QaVdYl[ R_Q]VZYR[',
	    'Po\\JlJ R]VjV R]ZjZ R]R][ R]EdEdP RdRdZ R]RjRj[ RWCWS[W RcI]P R_F_I RdIhOlP RZHXM RVSSZ RhFhI RTISO RWNUX RiC`D',
	    'PoRFlF RZJdJ RSMXM R\\TbT RfTkT RXJXT RZCZJ R[W[[ R\\M\\S R\\MbMcS RdCdJ RgKgT RhWl[ RbWc[ RUVSZ RkMfO RkQkT RXRRS',
	    'Po\\HlH R^KjK R\\NbN RdRkR R[UlU Rb[g[ R_N_R RdNdR RgSg[ R^DjDjK RdNkNkR R^VaY RWCWS[W R[IYM RVSS[ RTIRO RbQ[R RWNUW',
	    'Po\\HbH RfHlH R\\LbL RfLlL RaRgR RaVgV RdZgZ Ri[l[ R\\D\\[ R\\DbDbL RfDfL RaOgOgY RfDlDl[ RWDVTZW RZHXM RTIRO RWMUX RaOaU`Y',
	    'Po[HbH ReHlH ReLlL R[LbL R^PiP Rh[l[ R[D[[ R[DbDbL ReDeL ReDlDl[ RaRgX RVCVSYW RVSR[ RgPeTbW^X RTISO RYHWNUX',
	    'Po\\FiF R\\IiI RZKlK ReSiS R\\D\\I R\\DiDiI RaN]R RVOSR R^KYP RXTTX RfNaS RhTeX RTCXE RRHWI R\\NjNiR R_U[YS[ R_TcYl[',
	    'PoeSgXj[ RXI]M RZV]Y RdJgKkO RWWS[ R]Q\\T RdK`O RjFgH R`Q_T RkQhT RXKTN R]F[I RUQSU RUFTJ RaF_I RcVaZ][ ReOgW RYOWX ReDcL ReOcW RYCXL',
	    'PoaDlD RSH^H RSL^L RaLkL RRO^O RaOlO RSHSK RSD^D^O RaDaO RaHkHkL RiReV RXSTV RXLXO RXEXH RfLfO RfDfH R_R`UdXl[ R_Q]V[XXZR[',
	    'Po\\HbH RfHlH R\\LbL RfLlL RbYgY Ri[l[ R\\D\\[ RcQcY R\\DbDbL RfDfL RgVgY RfDlDl[ RWDVTZW RcOeQ RhRjW RZHXM RVSS[ RTIRO R_R^W RWMUX',
	    'Po]GjG R]KjK R[NlN R]UiU R`[c[ R]D]J R]Q]U RcUc[ R]QiQiU R]DjDjJ RhWl[ R^W[[ RVCVSXX RZHXM RVSR[ RSISO RVNUX',
	    'PoUG_G RUK^K RUO^O RUS_S RVFVU R[G[S R\\W\\[ RhWl[ RgQlU RZD[E RVWR[ RdP`U RbWc[ RfMiS RcGlFjK RVCSK RdC`K RfMcS RfIeP',
	    'PoXShS RVZjZ RSLSP RVVV[ RXOXS R_S^V RXOhOhS RVVjVj[ RSLlLlP RkEiG RUESG R^E\\G RbE`G RXH^J ReHlK RfDdH_J RYDWIRK',
	    'Po^LhL R[TlT R]F]R RbCaF R]FjFjR RWCWS[W RcL_P RdHcOhO RdUgYl[ RZHYM RVSS[ RfIfK RcRaW_Y[[ RTISO RWMUX RfG_H',
	    'Po\\IkI R\\LjL R[OlO R\\TkT R[WlW R\\D\\I RaDaI RcIcO RcTc[ RfDfI R\\DkDkI R`QaT RgQfT RWCVSXW RZHXM RVSR[ RSISO RVMUX',
	    'PoUGZG RdGiG RUJZJ RdJiJ RdDiDiL RhRdU R`J\\L RXRTV R]IaK R]DaF\\G R_QaVdYl[ RSNSQ R_Q]VZYR[ RZDUETNkNkQ',
	    'Po[FlF R`MgM R\\OjO R[UlU R\\H\\O R\\RdRd[ R`J`LgJgM R\\HjHjO RVCVSYV RcCcF RdUgXlZ RXHXM RbU`XZZ RSISO RVNTX RiQaR',
	    'Po\\FcF RdFlF R]MkM R]PkP R^XiX Rh[l[ R\\R\\[ R]J]P R`C`H RdJdW RgCgH R]JkJkP R[SlSl[ RVCVSZW RgUiY RZHXM RVSR[ RRHSO RVNUX',
	    'Po^HhH R[PaP RePkP R[SlS R[K[P R^D^H R[KaKaP RcQc[ ReKeP R^DhDhH ReKkKkP RcShXlZ RcS^XZZ RSIRO RYHXM RVDVQUVR[ RVSYV',
	    'Po[TlT RcPc[ R]Q_S RVCVTYW RfIkN RiQgS R^H[K RbJ]O R\\J`L RcTgXlZ RYHXM RVSR[ R`C_HbH`M ReEkEiIfMbN RcT_XZZ RTISO R`EcE_E RVNUX',
	    'PoaJiJ Ra[l[ RhPhW R[J]M RVDVTYW RfNlT R\\D^G RiElI ReJbM ReNbQ RZQ^Q^WZ[ RdU`V RjLgPbV RbF_I RYHXL RVTR[ RSISO RfXcX ReDiD RVNUX',
	    'Po\\FkF R\\IjI R[MlM R[SlS R\\[_[ R_O_[ RcFcM RVCVSYV RhOjQ R_C_F RgCfF RlYk[ RZHXM RiUfYbZ RRHSO ReNfX RbO[O RVNTX RcV[W',
	    'Po[JbJ R[NbN RZQcQ R[F[N R]C]F R[FbFbN RgWc[ RVDVSXX RfRjZ RiQeY RXHXM Ra[][ ReLhX RjLgW RSISO ReJgV ReJkIhV R^O^Q[Y RfCdN R]UbUa[ RVNTY',
	    'Po[FlF R`D`H RgDgH R`U_[hZh[ RcTfW RVCVSYW RaL\\Q ReNaR R^H[M RiUkY RYHXM R\\U[Z RfJcQ R]KjJjRfR RbJ^NgO RSISO RVNTY',
	    'Po\\FlF R]KlK R]QlQ R^TjT RbIb[ RgJg[ R^MjMjT RVCWTYX ReTkZ RcCdF RbT]Z RZHXM RVSR[ RhGfL RSISO RVNUX R\\F\\T[Z',
	    'PoiMlM R[MbM R[SbS R[E[M R^O^X R_EbEbM ReDiDjL RlJlM RfVl[ RVDVTYW RhUc[ R]C[F RdPhX RcOjPgX RVSR[ RSISO RYHXM R[I]I[I R_IaI_I RaE_E RbXZY RVNUX ReEdM',
	    'Po\\IlI R]ThT R`XhX R\\YdY Rf[j[ R\\D\\I RbDbI RbNbX RgDgI R]PgPgT R\\DlDlI ReVhY RWDWS[V R_KZP RZHYM RWSS[ RTISO R]P]T R_I]MlMlUk[ RWMUW',
	    'Po\\DbD RZFcF R\\IbI R\\KbK R[QbQ RSTgT R[M\\Q R[MbMbQ RVCVMYP RhChMlQ RkFjJ RSGSJ RXFWJ ReGdJ RXU^XbYk[ RfU`X\\YS[ RVHUP RhHgO',
	    'Po\\DjD R]J`J ReJiJ R\\M`M ReMiM R[GkGkJ R`XcY RbQdR R\\TiTdZ RVDVSXW RcDcNlR RYHXM RVTR[ RcNZR RTISO R[G[J RVNUX',
	    'Po`EfE R\\EdE R\\J\\M R`QgPgQ R\\JkJkM RhMlP R`R\\V R^N[Q RVDVSXW R^F^I RcFcI RbKeM RZHXM RVSR[ RiFhJ R`M`Q R_TbXfYl[ RSISO R_TiTeXbY[[ RVNUX RjCbD',
	    'Po\\FkF R[JlJ R]PiP R]TiT R]M]S R_V_[hZh[ R]MjMjS RbUeX RVCVSYW RiVkZ RcCcF RgGfJ R_G_I RYHXM R\\VZZ RSIRO RVNUX',
	    'PofHjH R]RjR R]UjU R]XjX R]P]X R]PjPjX R]EkEiH RVDVSXX RfYl[ RYHXM RVSR[ R_I]N[N R\\KcKbN`N RhIfNdN RdKlKkNhN RTISO RbG\\H R`Y[[ RVNUX',
	    'PogKjK R[KaK R[RaR RcTkT R[E[T R[EaEaR R`Wa[ RcNcT RcNkNkT RVCVSYW RdVf[ RiVk[ RYHXL RVSR[ R\\VZ[ RfEcK RTIRO RcEkEjK RVNUX',
	    'Po`IhI R]QjQ R^WjW R^ZjZ R]L]Q R^T^[ RdLdQ R^TjTj[ R]LjLkQ RWDWS[W RdCjI RcC]J R`M`P RgMgP RZHYM RVSS[ RTIRO RWNUW',
	    'PoaFkF R_JlJ R`NlN R`RkR R_UlU Ra[l[ RfCfI RfNfY R[J]M RVDVTYW R\\D^F RiKhM RZQ^Q^XZ[ RbKbM R^Yc[ RZHXL RVSR[ RTISO RVNUX',
	    'Po]HjH R]LjL R]NkN R_QjQ R]TlT R]E]L R`T`[ RbCbL RfCfL R]EjEjL RWCWS[W RdUhYl[ R[IYM RVSS[ RjUfW RdZ_Z RSIRO RWMUW R]N]U\\Z',
	    'PoZFlF R`MfM R\\OjO R]RiR R[TlT R_[c[ R\\H\\O R`J`M RcTcZ R`JfJfM R\\HjHjO RgWkX R]XYY RVCWTYX RcCdF RYHXM RVSR[ RTISO RVNUX',
	    'Po]DkD R^JaJ RfJiJ R^MbM RfMjM R\\PlP R\\H\\J R]S][ RbSb[ RdDdN RfSf[ R\\HlHlJ RWDWTYX RcPaU RTISO R]SkSk[h[ RZIWMUX',
	    'PoSE^E R`ElE R[J[O RgIgO RdGhKkN RhSeV RWTTW R\\GYJ^L RWGYIZLRO RgE_H RjGfI_K RgK_N RXFSH RXISK R_QaVdYl[ RYPVP RfPcP R_Q]VZYR[',
	    'Po[ElE R^JhJ R_PgP RiRlR RZTlT R[L[R R^G^J RcRc[ R_N_OgNhP R^GhGhJ R[LlLlR RVCVSXX RcCcE RcTgXlZ RYHXM RcT_XZZ RSIRO RVNTX',
	    'Po\\GlG R]OjO R\\RkR R[UlU R]J]O RaXa[ R]EeDdU R]JjJjO RhWl[ RWCWSZW ReWf[ R`L`N RgLfN RZHXM R]W[[ RTISO RWMUW RiCaD',
	    'Po[HlH R\\KiK RZNlN RYZlZ R\\T\\Z R`P`S RaTaZ RcCcN ReTeZ R]EiEiK R\\TjTjZ RVCVTYW RiPlS R\\QZS RePeS RYGXL RVSR[ RSISO RVNUX',
	    'PoZElE R\\HjH R\\MiM R[OjO RZRlR RbUlU R[YaY Re[i[ R[T[Z R[TaTaY RcMbP RcCcH RiSi[ RdWfY RVDVTYW RZKkKkM RYHWM RTISO RVNUX',
	    'PoSEkE RUGiG RWLgL RUNiN RRPlP R^SkS RTV\\V RdVgV RTRTV R[X[[ RTR\\R\\V R_C_G R_L_N RgQgV RhXk[ RaTbU RbXb[ RVXS[ RSJjJjL',
	    'Po]HbH ReHkH R\\KbK ReKkK R^PkP R^SjS R^VjV R^ZlZ R_N_[ R\\DbDbK ReNeZ ReDkDkK RWDWS[W R_O[S RZHYM RVSS[ R`K]R RTIRO RWMUW',
	    'Po[GaG RdGkG RcLlL R][_[ Re[i[ RZLaL_[ RgDhLi[ RVDVSXX RZPYT RkOlU RXHXM RaPcRbU RSIRO RVNTX R^D]L\\Q[Y RfLfRdY',
	    'Po[DcD RfHlH R[LbL R[PcP RZZlZ R[D[P R]R]Z R`L`P RaRaZ RfRfZ R]RjRjZ RgKjN RVCVSXW RYHXM RVSR[ R_D`H RSISO R[HbHbL RfCdL RVNUX',
	    'Po]GaG RUHYH ReHiH RUKYK ReKiK RSNSQ R[D[N R`I`L R[DcDcN ReDiDiN RVDTNkOkR RhRdV RXRWU RYDUD R_QaVdYk[ R`I]I^L`L]L R_Q]VZYR[',
	    'Po\\SlS R\\N\\Q RdQd[ R\\NkNkQ RgVkZ RVCVSZW R^IbJ RhHkL RkEjH R`U\\Z RbEaG R[E\\G RdEeG RYHXM RhDfK RRHSO R_C^K RVNUX',
	    'Po\\ElE R]HlH R_NjN R_QjQ Ri[l[ R^T^[ R_K_Q RbFbJ ReKeX RhFhJ R_KjKkQ R^TlTl[ RVDVTYW R`XhWiY RZHXM RTIRO RVNUX R\\E\\SZZ',
	    'Po\\KjK R\\MjM R[PlP R\\WjW RZ[l[ R\\D\\K RcDcP RcTcZ R\\DjDjK ReRfT RhRkT R]R[T RVDVSXW RaRaT R_F`I RfFfI RRHSN RYIYN RVNUX',
	    'Po\\GjG R\\JjJ R[MkM RZQlQ R_[c[ R\\D\\J RcSc[ R\\DjDjJ R^T`V RWDVSZW RgJfQkU R_J_Q\\T RhTfV ReWjZ RZHYM RVSS[ RTIRO R`XZY RWMUX',
	    'PoRElE RSJ]J RRL^L RZO^O RSQ^Q RWU^U RRUZU RXOXT RZCYGYLZO R[W[[ RdCdG RlSlV RhWl[ RcPfS RUXR[ RWLTP RaWc[ R`OdP RaT_U R`KiKiPjU RdHcR',
	    'PocEjE R^KhK RcMiM R_RiR R_TiT R_P_T RcCcM RdPdT Ra[hZh[ R_PiPiT RcVfW RVDVTYW RiWlZ R^W]Z RaWa[ R\\HkHkK RZHXM RVSR[ RTISO RVNUX R\\H\\R[Z',
	    'Po\\PjP R\\J\\P R\\R\\Z RcRcZ R\\JjJjP RWDVSZW R[F^I RbFdI RhFkI RkChF R]C[F RdCbF ReSgT ReVgW R^TaT R^V`W RlYl[ RVSS[ R_LgN RTIRO RfL^N R`YYZ RhYaZ RiSj[ RYHWMUW',
	    'Po[FlF R[MkM R^PcP ReTlT R^N^U RaFaM RaWa[ RfNfT RfFfM R[IkIkM RVCVSXV RjWk[ RcCdF ReWf[ RZHXM R]W[[ RSISO RkPeQ RcS]T RVNUX R[F[QYZ',
	    'PoZHdH R[OdO R[RdR Rg[k[ R[J[O R^W^Z R[JdJdO RcVcX RVDVTYV R`W`Y RVSR[ RZWZ[ RTISO ReIlIlUkZ RYIWNUX RhChOfX RcD\\E R`D`UZU RcU\\U',
	    'Po\\FlF R\\IlI RaLhL R\\NlN R^TkT R^WkW R^Q^W RaGaL RdCdF ReNeW RhGhL R^QkQkW RVDVSYW RgYl[ RYHXM RbX\\[ RTISO RVNUX R\\F\\QZX',
	    'PoaKeK RfPkP RZP_P RZSlS RaFaPdPc[ RbCbFeFeP RVDWTYX R[G]L R_M_Q RgGiL RjLkQ RcShXlY RjGgO R]D\\I R^H[P RiDhI RcS^XZZ RTISO RYHWNUX',
	    'Po]JkJ R^RiR R^UiU R`[c[ R^M^U RcUc[ R\\OjNiU R\\E\\GkEkH RhWkZ RVCVTYW R^X[Z RkLhN RcI]N RdJhNlO RZHXM R\\K_MZO RSISO RVNTX',
	    'Po\\ElE ReKlK ReVkV R][l[ R`U`[ RbGbQ ReSe[ RiGiQ RVCVTYW R^KbKcN RgKlO RgMdP RhIfO RVSR[ RaJ^P RSHTO RYHXM RcG^G RkGeH RVMUX R\\E\\S[Z',
	    'PocEjE R_KgK RcMiM R_RiR R_TiT R\\[l[ R_O_T RbVb[ RcCcM RdOdT RfVf[ R_OiOiT R\\HkHkJ RVDVTYW RZHXM RVSR[ R^V^[ R^VjVj[ RSISO RVNUX R\\H\\R[Z',
	    'Po[ElE R]KkK R^NjN R^QjQ R^TkT R^J^U R`C`G RaXa[ ReIeT RhChG RWCVSYW RjWk[ R^J[N ReWf[ RZHXM RVSR[ R\\W[[ RSIRO RVNUX',
	    'Po]GkG R_PiP R_SiS R`L`S R^MjLiS RWNWU[X RhVkZ R^I`K RfHlN RWWR[ RjHhK R`V\\Y RWCRH RVHZIVM RbI]N RTPSU RUF\\FZJ R[PZT RdC`K RTJWLRN RdSd[a[ RWPWW',
	    'Po\\HbH RfHlH R\\KbK RfKlK ReVhV R`YhY Ri[l[ R\\D\\[ R`S`Z R\\DbDbK RfDfK ReShShZ RfDlDl[ RWDVSZW RbN_Q RYHXM RVSR[ RcR`S RbOhNgQdQ RTISO R`VcV`V RVNUX',
	    'Po[GjG R\\IjI RZLlL RZPlP R[UcU R][a[ R[NaN_T R\\U\\XaXa[ RcGcL RVDVTYW R^D_E R^RZS RjTc[ RhDfE RgMjO R`P\\QcS RjRhW RYHXM RTISO RlXk[i[ ReMfX RVNUX',
	    'Po\\DbD RZGcG R\\IbI R\\KbK R[QbQ R[M\\Q R[MbMbQ RVCVMYP RhChMlQ RhSeW RWTTW RkFjJ RSGSJ ReGdJ R_RaVdYk[ R_R]V[YR[ RXFVIUP RhGgO',
	    'PoeFlF R[FcF RZLcL ReNkN ReQkQ R[RbR ReTjT R[VbV ReWkW Re[l[ R[N[[ R\\G]J R^C_F ReNe[ ReCeIkIkN RVCVSXW RYHXM RaG`L RSISO R[NbNb[_[ RVNUX',
	    'Po]GjG R]IjI R[LlL R\\OkO R[RlR R\\UlU R\\XkX R]D]I R^O^U R`J`N RcOc[ RgJgN RiOiU R]DjDjI RWDVTZW RZHXM RVSS[ RTISO RWMUX',
	    'Po_EbE R]ShS R]VhV R]Q][ R_D_H RbObQ R]QhQhU RdGjM RcEjFeK RiWeX RVDVSXX RbLjP RYHXM R\\GZK R\\HaH^LZN RaMZQ RbXkZ RSISO RbZ\\Z RVNUX',
	    'Po[FlF ReLjL R\\NkN R]QjQ R[TlT R]H]LaL`T R]HbHbL ReHfLfT ReHjHjL RVCVSYV RcCdF RdUgYl[ RYHXM RVSR[ R_W_[ RbU[X RjVfW RSIRO RdY^[ RVNUX',
	    'Po_IgI R[O^O RaOeO RgOkO R\\VjV Rg[j[ R[K[O R\\R\\[ R[K^K^O RaRa[ RaKaO RaKeKeO ReRe[ RgKgO R\\RjRj[ RgKkKkO RVCVSXW RcCfFlI RbC_GZI RSIRO RXIWNTX',
	    'Po\\HbH ReHlH R\\KbK ReKlK R^NiN R_UiU Rh[l[ R\\D\\[ R_Q_U R\\DbDbK RdLdZ ReDeK ReDlDl[ RdTiY RWDVTZW RdT_Y RZHXM RVSS[ R_QiQiU RTIRO RVMUX',
	    'PoZGkG R[KkK R\\NjN RZPlP R\\SaS R\\VaV R\\P\\Y RaCaG RdCdG RVDVTYW R^H_K R\\D^F RiDgF RfHeK RiVeZ RdSeVhYl[ RcSjShX RYHXM RSISO RVNUX RcKbPaZZY',
	    'Po\\NkN RdRlR R\\RbR R\\UbU R\\WcW R\\ZcZ Rg[j[ R\\P\\[ R\\H\\M RaIaM R\\PbPcU RfIfM RjOj[ RWCVSYV RhElI ReTfX RcFcH RVSS[ R^E[JkJjN RTIRO RYHWMUX RiD`E',
	    'Po`NiN R\\NfN RZVaV RcVkV RZRZU R]P]Z RZRaRaU RbCbM RgPgY R]EhEhJ RcRkRkU R`XbZ RVDVTYW R]E]JgKjO RSHSN RYHXN RcRdV RVNUX RaY[Z RjWkZeZ',
	    'PofFlF R]MbM RfMkM R^QkQ R^TjT R^WjW R^ZlZ R]I]M R_P_[ R\\FbDbG R]IbIbM ReOeZ RfIfM RfCfG RfIkIkM RWDVS[W R^P[T RZIXM RSISO RWNUX',
	    'Po\\GbG ReGkG R\\JbJ ReJkJ R\\LbL ReLkL R]QkQ R]SkS R]WkW R]ZlZ R\\D\\L R^O^[ R\\DbDbL ReNeZ ReDeL ReDkDkL R^OZS RVDVSXW RYHXM RVSR[ R^M]R RTISO RVNUX',
	    'PoZElE R]LiL RbPhP R^QeQ RaZjZ R^[h[ R\\[f[ R[M[S R_C_F RcHcP RcRcZ R]HiHiL R\\TiTiW R\\T\\WiXk[ RVDVTZW ReJiJ RTISO RYHXM R[MkMkRiR RVNUX R`J]I]K`JcJfJcJ_J',
	    'PoUElE RWI`I RbIkI RcPlP RWR^R RcRkR RcUlU RWU^U R\\G\\N RWP^O^W R^Y^[ RcNcW RfGfN RhXk[ RhLkM RfIaM RYXW[ R\\IWN RdXd[ R[J_L RdIiK RUEUPSX',
	    'Po^LiL R\\RkR R\\TjT R[WlW R[G[J R\\N\\R R^I^L RaYa[ RcCcG RcNcW R^IiIiL R\\NkNkR R[GlGlJ RjYl[ R^D_F RhDgF R\\YZ[ RVDVSXX ReYf[ RYHXM RVSR[ RSHSN RVNUX',
	    'PoZElE R[KaK ReKkK R[MaM ReMkM R^PhP R_UhU Rh[k[ R[H[[ R_C_G R[HaHbM RcNcZ ReHeM RgCgG R_RhRhV ReHkHk[ RcUgY RVDVTYW RcU_Y RYHWM R_R_V RTISO RaSaV ReSeV RVNUX',
	    'Po^JbJ R\\HjIlJ R^LbL RgLlL R^QlQ R]ZiZ R^N^Q RbNbQ RcScZ ReIeL RgNgQ R^U^VhUhW R\\DlDlH R^NlNlQ R^WgXi[ RVDVSYV RaQ\\U RZHXM RVSR[ RTIRO R^SlSk[ RVNUX R\\D\\QZX',
	    'Po\\DbD RdFlF R\\JbJ R\\MbM RdMlM R]RjR R]TjT R]WjW RVRXW R\\D\\M R]O]V R_D_G R_J_M R\\GbGbJ RdJdM RfJgM RiJiM R]OjOjV RdJlJkM RfWf[lZl[ RfGhH RYHXM ReCcH RTISO R`W`Y R_ZZ[ RVCVQUX',
	    'Po\\EkE ReKjK R\\Q`Q R[TkT RZ[l[ R[G[J R\\N\\R R\\N_N_R RcEcL RcTcZ RbNeNeR R[GlGlJ RhVkY R_VaY R_V\\Y RhVeY RVCVSXW RYHXM RiIeI RaK\\L ReNbNbReQaR RSHSO RhNkNgNgRkR RkNkRgR R]IaI RVNUX',
	    'PoUGYG ReGiG RUIYI ReIiI R[J`J RRSlS RSKSN RYLYQ R[D[K R[F^EcDcK RdLdQ ReDiDiK RUETKkKkN RYVWX RdM`Q ReVcY R`NgNjQ RUNZN\\P R[NTQ RYCUD R]RXURV R]G`G RbSfUlV R_UcYk[ R_V\\YS[',
	    'Po`HeH R\\UdU R\\WcW RZMZP R\\P\\T R\\V\\[ R]C]K RaEdEcK RdPdT RaIaKeJeK RiCiK RZMlMlP R_PbS RZF]F_I RgFjFlI RbO]S RaC`F RUDUSWW RYIWM R\\EZJ RiDgJ RjOfQ RjTfV RjXe[ RSIRO RUNTY RcZ[[',
	    'Po_F_Z RgEgKkZ RVGVQSZ R]FVG RhD[F',
	    'PohQlZ RWPR[ R^R_Y RhNWP',
	    'PoaMkM RaUkU RYGYZ RbEbU RfEfM RaEkEkM RkRkU R_TbXeZk[ R^D^M_U R_DTFTSS[',
	    'PoRPlP RUUiU RY[_[ R_K_[ RVKiKiV RfEkJ RWE_F_J RVESK RgD_D',
	    'PoaNfN RfQiQ RaVkV RYDYZ RbJbU RaJgJgN RaGjGkP RkSkV RbC_K R^R_VbYl[ R]D]O^V R^DTETRSZ',
	    'PoRQlQ RRUlU Rc[g[ RVLV[ R_K_U RVLhLh[ RfFkK RVGRK R^F_I RVETJ R\\EWE RgC\\E',
	    'Po^EdE RWE`E RWJhJ RRNlN RfEkJ RXUR[ R_F_I RVETK R[NWW R[JXU RYShScW_YX[ R[R_WcYl[ RgC`D',
	    'PoTEbE RUOjO RSJSM R_F`J RSJlJlM R[SS[ RXFYJ R]MXW RgFeJ R[S]VbXk[ ReD`D RZThSeV`YY[ RiDcD',
	    'PoWEbE Rf[j[ R^V^Z RWJfJfO RfUhX RgElJ RbVcY R^F_I RVETJ RWJWSSZ RZVX[ RWNiNiS RVSlSk[ RdD_D RhDbD RWMVV',
	    'Po_DeD RY[_[ R`X_[ RVIgIfL RVLjLiO RaPbR RXFZH RePgR RTEZE_E`H RhEdH RXPWS R]P]S RUOlOkShS RSUjU_W RVIUQ RhCbD',
	    'PoTJ\\J RRZ^Z RRER[ RTMWMWQ RRE^E^[ RfEf[ RWMTMTQWQTQ RZF[QXVSU RZN[X RjDiOk[ RjDaFaR`[',
	    'PoTMjM R_RlR RTR]R RTU]U Rc[h[ RTITM RTPTZ R[I[M RTP]P]U RcIcM RhOh[ RTIjIjM RVFXH RaTdW RZW^[ RhEdI RSE_E^H RbD^D RiCcD R\\YR[',
	    'PodDkK RZDSL RYK[QaWk[ ReKcQ]VS[',
	    'PoaWgW R]P][ RVPgPgW RcDkG RZDSG RYE\\IaLeMlN RgEcI^KYMRN',
	    'PoWUhU RW[j[ RWOWZ R_O_U RWOhOhU RkXj[ RcDiG RZCWFSH R[G]JbLkN RcG`J[LRO',
	    'Po[MdM R^TcW RcDkG R_KVO RcNYS RcQYV R[DSG RdF_IVK RYN_QTT RaSjRgVbXZ[ R[F_IcJlL R`IRK RcXV[',
	    'PoUR]R RUU]U RWX]X RUY[Y RRYYY RUOUY RRO^O][ RbOb[ RbOkOgTjU RcCkG R[DSG RcG^JUL R[GaJlM R_JRM RjYfY',
	    'PoXN_Vj[ RUEiK RgD_ISL RfN_VWYR[',
	    'Po_LiL R_SiS R[[l[ R_E_[ R_EiEi[ RXT\\X RWUR[ RXHRN RSF\\J RTRXT RZCWJ RZOVX',
	    'PoRGlG RXK[N RUPYU RfQjU RfKiN RiIcN RiOdU RZIUM RZPTU RcJgL RdQgR RUJYK R_C_NbUfYl[ R]TYXR[ R_I`S R_I]T',
	    'PoSElE Re[i[ RUKU[ R_E_[ RUKjKj[ RdUfW RXN\\O RYU\\W R[TWY RfLaQ RfTaY R\\LWQ RbNfO RdGlJ RYGRJ',
	    'PoWKiK RSQiQ RWDWK RiCi[ RYVT[ RYQWY',
	    'Po\\JlJ RTKYK RRQYQ RTDTK RYCY[ RdCd[ RhTlW R_T[W RdIiU RdI^V RTQS[',
	    'PoTKYK RRQYQ R]TeT Re[i[ RTDTK RYCY[ R]K]V R]KeKeT R[FkFj[ RTQS[',
	    'Po]IkI RTLYL R^NjN RRQYQ R\\TlT RTETL RYCY[ RdId[ RiCgG R_DaG RTQSZ',
	    'Po[GlG RbLhL RTLYL R^OkO RRQYQ R[SlS RTDTL RYCY[ R^D^O RbCbK RcOc[ RhChK RhWlY RcRjY RTQS[ RcS\\ZZZ',
	    'PoTLYL R^OjO RRQYQ R^RjR R^ZjZ RTDTL RYCY[ R^L^[ R^LkKjR R]VjUj[ RcHeJ RdEkL RdE\\L RTQS[',
	    'Po\\FlF R^KlK RTKYK R_NjN RRQYQ R_TkT R_WkW Rh[k[ RTDTK RYCY[ R_Q_[ ReCe[ R_IjIjN R_QkQk[ RTQS[ R\\F\\T[[',
	    'Po[FkF RSKYK R[NlN RRQYQ RaWfW R]ZkZ RSDSK RYCY[ R]Q][ RaTaW RcCcN RaTfTfW R]QkQk[ R^JaL RhIkL RhHfL R^H]L RTRS[',
	    'PoWKlK RbCbJ RWDWNUTR[ RVQfQf[',
	    'Po_DkD RTK]K R_LkL RZCZK RTQ[Q[[ RTDTSS[ R_D_Q]Z RaLdTjZ RkLgUbZ',
	    'PoTK[K R]MkM R\\SlS RXCXK RTQYQY[ RdCd[ R^EaK RjEhK RTDTTR[',
	    'PoUL[L RfNkN R`ZkZ RUQ[Q[[ R[D[L R`Q`[ R`QkQk[ RcJ_N RdEbL R_ElEkN RUCUSSZ',
	    'PocGlG RaGjG R]HfH RTK[K RbSjS R^SgS R\\SdS RYCYK RTQZQZ[ RbFdKfMjN ReOhP RaQcWfZi[ RfDjE RkLkN RhU^Z R`ObW RaCcK RhK]N RkYl[i[ RTDTTR[',
	    'Po^KkK RTK\\K R^OkO R\\UlU RYCYK RTQZQZ[ R^F^O RgQg[ R^FkFkO RdN_S RcCbF RTDTSR[ RdFdKdP',
	    'Po^FjF RTK[K R\\TlT RXCXK RTQZQZ[ RdCdJ RgVk[ R`McN R^PbQ RdUaY][ R]JkJjN RfMdV RTDTTR[',
	    'PoTKZK RfQiQ R`[g[ RXCXK RTQYQY[ RaTa[ RgXg[ RhUkX ReL`P R`E[I RbI\\N R_JgM RaReT RbGaK R]U[Z RgGcN R^GkGjP RTDTSR[',
	    'PoTKZK R_[b[ RXCXK RTQYQY[ R\\N\\[ R\\NbNb[ R^P`R R^U`W R^EdDjJ RcD\\K ReTh[ RhTl[ R_KhJlK RfNeT RjNiT RTDTTR[',
	    'Po]IlI RTK[K R]SbS RgSkS R]ZkZ RYCYK RTQZQZ[ R]N][ ReDeY RgNkNk[ RbM]N RTDTTR[ RjD`E',
	    'Po]KkK RTK[K R^UlU Rj[l[ RYCYK RTQYQY[ RcOcZ RgOgZ R]FkFkK R]PlOl[ RcCeF R]F]R[Z RTDTSR[',
	    'Po[GlG RTK[K RcLhL R^OkO R\\SlS RYCYK RTQYQY[ R^D^O RcCcK RdOd[ RhChK RcS]Z RdSjYlY RTDTTR[',
	    'Po]FlF RTK[K R]PlP Rd[h[ RYCYK RTQZQZ[ R\\L\\O RdCdF R\\KlLlO RaW][ R`G`K RhGgJ RdMcQ`X RbUiTh[ RTDTTR[',
	    'Po[FlF RTKZK R]MjM R]PjP R[TlT Rc[g[ RXCXK RTQYQY[ R]I]R RcCcQ RgRg[ R]IjIjR R^VaY RfDjE RTDTTR[',
	    'PoTK[K R]PkP Ra[h[ RXCXK RTQZQZ[ R]F]P RbTb[ RcCcF RhXh[ R]FkFkP RcIgIbO RiUlZ RdG_JgM RbRfT R^U\\Z RTDTSR[',
	    'Po]EeE RTK[K R]KkK R]OlO R_UkU R_XkX Rh[k[ RXCXK RTQYQY[ R_R_[ ReMe[ R]GkGkK R_RkRk[ RgMkN RTDTTR[ R]D]R[Z RjCbD',
	    'Po\\FlF RTKZK R^KlK R_NjN R_TkT R_WkW Rh[k[ RXCXK RTQYQY[ R_Q_[ ReFe[ R_HjHjN R_QkQk[ RdCeI RTDTTR[ R\\F\\T[Z',
	    'Po[ElE R]HkH RTKZK RfLkL R\\NkN R]SjS R]UjU R]XjX RXCXK RTQYQY[ R\\J\\N R]P]X RdCdG RfJeL R]PjPjX R\\JkJkN RfYl[ RbK^L RaY[[ RTDTSR[',
	    'PoTEkE RUOlO R^[c[ RcEc[ RbN[URY RWHUP',
	    'PoWNgN RWQjQ RRUlU R`[d[ RSGSJ RWQWU RWJWN RdQd[ RWJgJgN RSGkGkJ RWDYG RhDeG R_C_G RdT^XT[',
	    'PoVIjI RRQlQ R_C_[ RXDTM',
	    'PoVIjI R_C_[ RWESM R[RSR R`QWR RcQ\\R RlPfP_Q',
	    'PoTJ]J Ra[k[ RXCX[ RaCaZ RlWk[ RjJgK`O RTERN R]PRR',
	    'PoTJ]J Rf[k[ RXCX[ RaW^[ RUERM R]PRR R^JlJlVk[ RdCdMbV',
	    'PoVOjO RRUlU R_K_[ RSJiHkK R[CZFWI RcEhG RWKUQ',
	    'PoSJ[J R_[k[ RWCW[ R_F_Z ReCeU RlWk[ RkI[M R[PRR RkHjSgT RTERN',
	    'PoTJ]J R]LkL R\\ZlZ RXCX[ RdCdZ RTERN R]QRR',
	    'PoVNiN RRSlS RSFSK R_C_G R_I_[ RSFlFlK RWLSQ RXIVN',
	    'PoSJ\\J Re[j[ RXCX[ RbV][ RdQ`Y RTERN R_J]R R\\PRR R^FlFlSj[ ReFeLcU',
	    'PoTJ[J R]JlJ R`YkY RWCW[ R`J`Y RdDeG RTFRN R[QRR',
	    'Po]HlH RTJ\\J Rd[h[ RXCX[ RdDdG R\\PRR RTFRN RbHbOjNjUi[ RbL`W',
	    'PoSJ[J Rc[k[ RWCW[ RcFc[ RlXk[ R[PRR RTESN RiD\\G RjJ\\M RlQ[S',
	    'Po_JlJ RSJ\\J RXCX[ RaD]N R\\PRR RTERN R_JcUgYm[ RiJfTaX[[',
	    'Po\\HkH RSJ[J Rh[l[ RXCX[ RcCcG R_LhLh[ RlWl[ R\\Z[[ R\\PRR RTERN R_L_S^X',
	    'PoTJ\\J RXCX[ RaC]M R\\PRR RTERN R_HmHlPj[g[ RdHaP]T RiHcV_[',
	    'PoSJ[J R_JjJ R\\RlR RWCW[ RdDd[ R`E]N R[PRR RTERN',
	    'PoSJ\\J RXCX[ ReWi[ ReDiG RaX\\Z RgQ_Y RcRgZ RiMfS RbMeX R\\PRR RlWk[i[ RTESN RbCcK]L RaGdU RjJcK',
	    'PoTJ\\J RXCX[ ReCe[ RbT]Y ReObU RTFRN RkXhX R\\QRR R^I^PlQkX RkG_I',
	    'PoSJ\\J RgQlQ R^YlY RXCX[ R^E^[ RgEgQ R^ElEl[ R`S]T RTERN R\\PRR RcEbLaQ',
	    'PocHkH R`HiH R^HfH RVSiS RRVlV RWFWO R_O_[ RYDRK RXPTT R_EbK R[HbIdMhO RdDhE RkLkOhN',
	    'PoTJ\\J R^JlJ R_ZkZ RXCX[ R_Q_[ ReCeQ R_QkQk[ RTFSN R\\PRR',
	    'PoTJ\\J R^QlQ R^ZlZ RXCX[ R^I^[ ReCeY R^IlIl[ R\\PRR RTESN',
	    'PoSJ\\J R^SeS Rd[i[ RXCX[ R^K^U R^KeKeS R\\FkFj[ R\\PRR RTERN',
	    'PoTJ\\J R_JlJ R^RkR R\\ZlZ RXCX[ RdCdZ RTFRN R`E]N R\\QRR',
	    'PoSJZJ R[NlN R]WlW Rd[i[ RWCW[ RbPfT RbGfK RZPRR RTERN R_EjEjTi[ R_E_I^K^O]W',
	    'PoSJ[J R^LlL R[ZeZ RXCX[ R^E^V R\\PRR RlWkZiZ RTESN RdU[V RfEeMgX RiD^F',
	    'PoTFkF RVQiQ RRVlV RSLSP R_N_[ RSLlLlP RXOTS R`C^HjJ R]HUK',
	    'PoTHjH RRMlM RYSfS RTWjW R_O_[ RWDYG RfDcG RZPWT RcMfPlS R^D[LVQRS',
	    'Po]EdE RSJ[J R^MiM R]TjT R[ZlZ RWCW[ RcMcZ ReFkM RbF[M R[PRR RTERN',
	    'PoSJ[J R]RlR R`[d[ RXCX[ R]G]K RdPd[ R]GlGlK R_LiKeO RdCdG R\\PRR RTERN',
	    'Po^GkG RSJ[J R\\LlL R\\QlQ Rc[h[ RXCX[ RdCdL RhLh[ R_TbW R\\PRR RTERN',
	    'Po\\ElE RTJZJ RfRlR R]YlY RWCW[ R]J][ RbEbP RfEfQ R]JlJl[ R`R^S RTFRN R[QRR',
	    'PoSJZJ R\\IlI R`[k[ RWCW[ R_C`O`Z RlWk[ RdDiF RiLbO RiPcS RjTbW RZORR RTESN R_K]W',
	    'Po\\ElE RSJZJ R]SkS R[ZlZ RWCW[ RdSdZ RcLeQ RgLjO R_G]L_Q RjGhL RdGbL RZORR RTESN',
	    'PoRFlF RVSiS RRWlW RSKSN R_C_F R_P_[ RSKkKkN R^FZI RXQTU RUOeNhO RcG_K[M RWH^LXN',
	    'Po\\DkD RSI[I R[OlO R]ZjZ RWCW[ R]S][ R]IjIjO R]SjSj[ RZPRS RSERN RbD`O',
	    'Po_HkH RSJ[J R\\MlM R^ZkZ RXCX[ R^R^[ RdCdQ R^RkRk[ R`D]K R\\PRR RTESN',
	    'PoUIkI RXL^L RdLkL RXO^O RdOkO RZTkT RWWlW RaKaO RaQa[ RUEkEkI R[QWU RUEUQTY',
	    'PoRH`H RfOjO RVSiS RRVlV RSEYDYO R_O_[ RdDdL RjCjO RYH^M RYHSN RXPTT R^CVD',
	    'PoRH^H RfNjN RVSiS RRWlW RYDYN R_O_[ RXHRN RWQTU RWHYJ^L RbC`J RfFcM RaGkGjN R\\CTE',
	    'Po]GkG RSJZJ R\\NlN R^ZjZ RWCW[ R^R^[ RdCdG R^RjRj[ R`H`M RhIfN RZPRS RTFRN',
	    'Po]GkG RSJZJ R[OlO R^WeW Re[i[ RWCW[ R^R^Y R^ReReW RiOi[ RcIkL RcHaK\\M RdCcJ R[QRR RSFRN',
	    'PoSJ[J R_LiL R`ViV Rg[l[ RWCW[ R]E][ R`O`V RdLdV RhOhV R]ElEl[ RaGbJ RgFfL RZPRS RTFRN',
	    'Po\\GjG RSJZJ R]NiN R]RiR R]ViV RZZlZ RWCW[ R]J]Z R]JiJiZ RcCbJ RZPRS RTFSO',
	    'PoWGhG RSKkK RTR]R RaRkR RWV]V R_VlV RRWYW RYMY[ R_C_N RfMf[ RYEUI RUNRT RbN_S',
	    'Po^IjI RSJZJ R\\MlM RdWgW R`[j[ RWCW[ R`R`[ R`RgRgV RhNlS R^E`G RiDgG R`N\\S RjXj[ RcH_Q RdDaO RZPRS RTFRN',
	    'Po\\FlF RSJ[J R^NjN R\\UlU R_[d[ RXCX[ R^I^N RdCdF RdTd[ R^IjIjN R^QjQdS R\\PRR RTESN',
	    'Po^FjF RSI[I R\\TlT RWCW[ RdCdJ RhXl[ ReWiX R]P`Q R_MbN RdU`Y\\[ R]JkJjN RZPRS RSERN ReLePdV',
	    'Po]HjH RTJZJ R]MjM R]PjP R[TlT RWCW[ R]D]T R]DjDjT RfWjY R`W]YZ[ R[QRR RTFRN',
	    'PoSJZJ R\\KbK RdKkK R\\PbP R\\UbU RWCW[ R\\E\\[ R\\EbEbK RdEkEkK RgVl[ RiUc[ RePhY RcOkOgX RZPRR RTESN',
	    'PoSJZJ R`JlJ RbMjM RaQkQ R`UlU Rb[l[ RWCW[ RfCfY RbFjFjM R\\XZ[ RZORR RTESN RZF_F[N_N]W R[R]W`Zh[',
	    'PoSE]E R`GlG RRH^H RSK^K RdPhP RVSiS RRVlV RXCXH R_P_[ RhChO RbIdL RXQTU RXJYNRO R^NYNUO',
	    'PoSJZJ R^KlK R_UlU RWCW[ R^F_Q_[ RdOdZ RhOhZ R^FlFlK RdCfF R[QRR R_OlOl[j[ RTERN R^N\\Z',
	    'PoSJZJ R[LcL RWCW[ R`E`[ RiDi[ R_NcR RdMgP ReGgJ R_K[U RbD[F RZORQ RTERN RlSbU',
	    'Po]HiH RSJZJ R]LiL R]PiP R[TlT RWCW[ R]D]O R]DiDiO RfQjR RcSdWgYl[ RbV`Y[[ RZPRR RTFRN RcQbX',
	    'Po\\FlF RSJ[J R_MjM RaWhW Rh[l[ RXCX[ R]P][ R_I_M RaSaY RdCdF RaShShW R_IjIjM R]PlPl[ R\\PRR RTERN',
	    'PoSJ[J R_LhL RgNkN R^WhW Rg[k[ RWCW[ R_H_L RhHhL RhThW R`O[T R`E\\I RcM]O RcY][ R^T^W RcScY R[PRR RTERN R_FlFkN R_RlRk[ RcGcM',
	    'Po^GkG RSJZJ R_JiJ R\\TfT RWCW[ RaOa[ RlXl[ RaTdW ReOdR RbT\\Z R^O^S R_C\\J R[ORR RTERN R\\MiMiUj[',
	    'Po]FkF RSJZJ R\\KlK R_RjR R_VjV Rh[j[ RWCW[ R`M`[ ReNe[ RgCgK R^OkOk[ RaCaJ_N[QRR RTERN',
	    'PoVRiR RRVlV RSLSP R_N_[ RSLkLkP RkEiG RUESG R]E[G RbE`G RXH]J ReHlK RXNUT RfDdH_J RYDWIRK',
	    'PoSJ[J R^JkJ R^NkN R]QlQ R^ZjZ RWCW[ R]F]I R^T^[ RdHdT R^TjTj[ R]FlFlI RcCfF RZPRS RTFSO',
	    'PoUElE RWI`I RbIkI RZTjT RVWlW R\\G\\O RaQa[ RfGfO R`C`E RfIiLlN RfIaN R\\IWN RZQYU R[J`L RUEUPSX',
	    'Po]GiG RSJZJ RWCW[ RdLjKkM RfDiH RfHdL RaC]G R\\H[L RcMlQ RYPSR RaNZQ RcQ\\S RfS]W RiVbY\\[ RTFRN R`JaLZL',
	    'PoTG^G RaGlG RWI^I RUK`K RXPZP RVTiT RRXlX RZKZP RTD^D^G R_Q_[ R^M_O RbJeL RWMUP RXRTV RhChPeP RTDTJSP',
	    'PoTF^F RbGlG RSI^I RUPkP RYTiT RVWkW RYCYN R`Q`[ RYHTM RZQYU RbGeLlN RYK^L RcCaH RiGeL_N RUQTY',
	    'PoZGcG RSJYJ R[MbM RVCV[ R[I[M R[IbIbM R^C_G RgVb[ RfRjZ RiPeY RYPRS RfDcO R[QbP_S^[[[ ReJhW ReIkIhV RcUYV RSFRN',
	    'Po\\FkF RSJZJ R[KlK R]QjQ R]TjT R\\WkW RZ[l[ RWCW[ R]M]T RcMcZ R]MjMjT RgGfK R_G`J RcCdF RZQRS RTFRN',
	    'Po\\HlH RSJZJ R_OhO R`XgX RWCW[ R\\Q\\[ R`I_O R`T`Z R`TgTgX R_LhKhO RfHkM RaH\\N R\\EdE_L RZORR R\\QkQk[g[ RSERN RiDaE',
	    'Po_GiG RSHZH R\\PkP RTPYP RVTkT RRWlW RRHRLZLYP RRDZDZH R_D_G R_R_[ RdGdP R_DiDiG R]JjJjM R]J]MiNkQ RXRTV',
	    'Po\\FkF RSJZJ R]IjI R[MlM R[SlS RWCW[ RcFcM RlYk[ RgXj[ RgOkP RgCfF R_C`F RjTfYbZ RZORQ R_O_[\\[ ReOgY RTERN RbO[O RcUZW',
	    'Po]DlD R_HdH RfHlH RSJ[J R`NjN R`QjQ RWCW[ R_S_[ R`K`Q RbFbI ReKeX RhFhI R`KjKjQ RaXhWiY RZPRS R^TkTk[h[ RTFRN R]D]S[[',
	    'Po[ElE R]HkH RSJZJ RfLkL R\\NkN R]SjS R]UjU R]XjX RWCW[ R\\J\\N R]P]X RcCcH ReJfL R]PjPjX R\\JkJkN RaL^L RfYl[ RZORR R`ZZ[ RTERN',
	    'PoSJZJ R\\PjP RWCW[ R\\J\\P R\\R\\[ RcRc[ R\\JjJjP R[F^I RbFdH RgFjI RjCgF R^C\\F RdCaF ReSgS ReVgV R_SaS R^VaV RlYl[ RYQRR RgK_N R`Z\\Z RgZb[ R_KgN RiSj[ RSFRN',
	    'Po[IkI RSJZJ R[NcN R\\SbS R\\VbV Re[l[ R_[b[ RWCW[ R[D[H R\\P\\[ R\\PbPb[ ReSe[ R[DkDlH ReJeQkPkQ RlXl[ RaKbN R_J[N RZQRS RkTeV R`DaI RfDfI RkLeM RTFRN',
	    'Po[FlF RSIYI R[MkM R^PcP ReTlT RWCW[ R^N^U RaFaM RaWa[ RfNfT RfFfM R[IkIkM RcCdF ReWf[ RjWk[ R]W[[ RYQRS RkPeQ RcS]T RSERN R[F[QYZ',
	    'Po\\HiH RSJYJ R\\KiK R[NjN R[QkQ R_[c[ RWCW[ R\\E\\K RcSc[ RgKgR R\\EiEiK R^U`W R`K`R\\U RgUeW RgSlU RcVjY RYQRS RcV[Y RTFRN',
	    'Po\\FkF R]IjI RSJZJ R[LlL R[PlP R[UcU RWCW[ RcFcK RlXk[ RgXj[ RiUc[ RgCeF R_C`F RgNjN R]PcS RjRgX R`N_RZS R]U\\XbWa[^[ RZPRR ReMgY RTERN RbM\\N',
	    'PoUG^G RbGlG RUI^I RbIkI RUL^L RbLkL RUO_O RbOlO RVTjT RRWlW RUFUO RZEZO R_Q_[ RbFbO RgEgO RXRTU RVCSI RcC`I',
	    'Po_HjH RgOkO RfQlQ RVTjT RRWlW RYDYJ R_Q_[ RfKfQ R_EjEjK RcL_P R_E_KiKgO RWRTU RRGZG]J RVJUN]P RXHRK]LZOWPRQ ReCeGcM R\\CUD',
	    'Po[FlF RSJYJ RaJfJ RaMfM RaOfO RhPlP R_UhU RWCW[ R[J[P RaHaO RaHfHfO RhHhO RgDeF RfQlS RYPRR R^H^OZP R`Q[T RaU[W R`WeYk[ RkHhHkHkLgL RgWbY[[ RTERN',
	    'Po\\FbF ReGlG R\\HbH RTIZI ReIkI RTLZL ReLkL RTO[O R\\ObO ReOlO RWTjT RRWlW RUEUP RTGYFXO R\\L]O R_Q_[ R\\LbLbO ReEeP RhFhO RWCXF R_C_F RhCiF RXRTU RUCRI ReCeJ R_J\\J R]J`J^J',
	    'PoRLlL ReEhH R_C_L[UWYR[ R_KcUhYk[',
	    'PoW[^[ R_M\\QSU RaERL RVDZF_K`P`U_Z',
	    'PoSHkH RWVR[ R[C[M^SbWj[ RcDhF RZNUY RfMcT^XY[ R[JWV R[GXT',
	    'PodPjP R_[k[ RS[X[ R`F`Z R_FjFjP RUJRK R[CUJ RYMRT RlVk[ RSDXIZOZVYZ',
	    'Pog[l[ R[JhJh[ RlWl[ R^WZ[ RYERK RWORT RUEXLVQ R_R\\Y RWZS[ RWHYOYVXZ RaCaL_V',
	    'Poh[l[ R[IhIh[ R^WZ[ RZDSK RXLWORT R[OcS RlWl[ R`R\\Y RW[S[ RSDVGXJYOYVX[ RaCaL_V',
	    'Po[IlI Rc[g[ RgCg[ ReM\\V RZCSJ RYNRT RgHdN RW[S[ RSDWHYNYVX[',
	    'Poc[j[ RaNfS RZDTK RYMRT RaC]M RX[T[ R`HlHlSj[ RTDWGYKZOZVYZ',
	    'Po]EkE R\\NlN RT[X[ RdEd[ RZESL RYMRT RTDWGYJZOZVYZ',
	    'Po]HlH R\\[k[ RdP\\X R]MhMcR RlWk[ RZDSK RXNRT R_C\\K RW[S[ RSDVFXJXOXVW[',
	    'Po[LlL RYCY[ RSGWK RgEjH RYORU RcCcLaQ_V[[ RcLdQfVk[',
	    'Po^HlH RdDdF RYERL RXNRT RWZS[ RSDWHYOYUXZ R^H^Q\\Y',
	    'Po\\UhU Re[j[ RS[W[ RZDSK RXNRT R^H^PlPj[ RSDVGXJYOYVX[ R]EiEhP',
	    'Po[JlJ Rc[k[ RcCcNd[ RgDjG R_VZ[ RZDSK RYNRT RbP^X RlVk[ RW[S[ RSDVGXJYNYVX[ RbHaT',
	    'Po\\HlH Rg[l[ R`LgLhZ RYERK RXLVPRT RcCcF R]Z[Z RWZSZ RlVl[ RSDWHYOXVXZ R`L`S_X',
	    'Po[HlH RgWkW R\\M\\X RcCc[ R\\MkMkW RZDSK RXNRT RW[S[ RSDVFXJXOXVX[',
	    'Pof[k[ R[[_[ RXKXY R`J`[ RfDf[ RlVl[ RYDRK RVPRT R[EdE_J RTDWLUR RcNZP RV[S[ RVGXQ',
	    'Po]EkE R^OlO Rb[g[ RhEh[ RYERK RWORT RUFXLVR RhN`W[X RW[S[ R_G^N RWHYOXVX[',
	    'Po[LlL RfVk[ RgDkH RaUZ[ RZDSK RYNRT RdQhY RcCcP_W RcJgW RcLaU RW[S[ RSDVGXJYNYVX[',
	    'Po\\IlI RcIc[ RcEdI RYDRK RYLXPST RcNiP RX[T[ RTDWGYKYOYVXZ',
	    'PoSLZL R\\LlL RRQZQ RSDSK RZCZ[ RfVk[ RgEjI RaU[[ RcCdPhY RbP_X RcL`V RcIeU RUQTY',
	    'Po]LjL R[LgL Rf[l[ RgNg[ R^WZ[ RZDSK RXNRT RfGiJkM RlWl[ RaC]L RW[S[ RSDWGYNYVXZ R_N^X',
	    'Po\\FlF R]OkO R[ZlZ RdFdZ RZESL RYNRT RX[T[ RSDXHYOYVX[',
	    'Po\\OlO RZZlZ R\\EiEiZ RZDSK RYNRT RW[S[ RSDVGXJYNYVX[ RaEaJ`L`O`R^Z',
	    'PoeUk[ RaU\\[ RZDSK RYNRT RdCdPgX RkHhO RcO`W R^I\\P RW[S[ RSDWHYNYVX[ RcKbT',
	    'Po\\OkO R[ZlZ R]FdFdZ RYFSL RYNRT RX[T[ RSDXHYOYVX[ RjEaF',
	    'Po\\RlR R\\I\\T RdCd[ R\\IlIlT RZCSJ RYNRT RSDWHYNYVX[S[',
	    'Po[JkJ RcCc[ RhSlW R^SZW RZDSK RYNRT RcIhT RcI^T RW[S[ RSDWHYNYVX[',
	    'Po^D^U R^DjDjU RhXk[ ReVhW RZDSK RXNRT RcU`Y[[ RW[S[ RSDVGXJYOYVX[ RdHdOcV',
	    'Po\\FkF R[ZlZ RdIdX RfKkQ RZDSK RdI\\Q RYNRT ReFcK RW[S[ RSDWHYNYVX[',
	    'PoVHhH RVLhL RVPhP RRTlT RVDVP RVDhDhP ReRgT R_TcYk[ R_T\\XXYR[ R_P^V',
	    'Po^EjE R\\LlL R`[c[ RdLd[ RZDSK RYNRT RhPlX R^P[X RW[S[ RSDWHYNYVX[',
	    'Po]JkJ R]QkQ R[ZlZ RdCdZ RZCSK RYNRT R_E[M RW[S[ RSDVGXJYNYVX[',
	    'Po_RfR RfTjT R_[k[ RS[W[ R_K_Z R_KfKfR R_GjGjT RlWkZ RZDRK RXNRT R`C\\K RSDVGXJYOYVX[',
	    'Po]LlL R]SlS R]E]T ReEe[ R]ElElT RZDSK RYMRT RXZT[ RTDXHYOYUXZ',
	    'Po^HlH R_[k[ R_K_[ RdIdW RlXk[ RZDSK RYNRT R_C[L RjMZQ RW[S[ RjLjUgU RSDWHYNYVX[',
	    'PoZDRK RXLWPRT RfTh[ RW[S[ RiDiLiOjY RUEWJXOXVW[ R\\E\\R[Z RiD^E RbGcX]Y RgX`Y',
	    'Po]PiP RbWe[ R`WcX RbKdN RcDlM RhRbX RZDRK RXLWPRT RcE[M RWZT[ RUEXJYOYVXZ',
	    'Po\\MkM RXKXY RfCf[ R[GkGkM R_V[[ RYDRK RWORT RTEXLUQ RkYhY RW[S[ RWGXQ R\\M[SlRkY RaCaP_W',
	    'PodCdN RZCRK RXNRT R`ObTfYl[ R]HkHjL R]OjOhScX][ RW[S[ R]H]Q[Y RSDVFWJXNXVW[',
	    'Po]KkK Ra[k[ RbMbZ R]EkEkJ RlWl[ RZCSK RYNRT RjOaT RW[S[ RSDWHYNYVX[ R]E]R[Y',
	    'PoXKXY RZK_P RdW`[ RYDRK RWLVPRT ReRbZ R_D[L RaK[W RW[S[ Rj[g[ RbScVYW RTDWIXR RbIlIlTk[ RfCfNdV',
	    'Po[H[K RcDcH R[HlHlK RZCSK RYNRT RhSl[ R_R[Z R_O]X RW[S[ R`MgMiX RSDWHYNYVX[',
	    'Po_TfT Re[j[ R_L_V R_LfLfT RZDRK RXLWPRT R`C\\L RW[S[ R^HlHlSj[ RSDVGXJYNYVX[',
	    'Po]IlI R]I]W RgDiG RZDRK RXNRT RjMgT`Z^[ RcTZW RW[S[ RlWk[i[ RdCdJfW RSDVFXJXOXVW[',
	    'Po^LiL R^SiS RZ[l[ R^E^[ R^EiEi[ RZDRL RYNRT RUFYKYWX[T[',
	    'Po^MiM R^UiU RZZlZ R^E^T R^EiEiT RZDRK RXNRT RW[S[ RSDVGXJXOXVW[',
	    'Po^PlP R^YlY RS[X[ R^H^[ R^HlHl[ RZDSJ RYMRT ReCcH RSDWGYKYOYUX[',
	    'Po[IlI R]ZjZ R]Q][ RcCcQ R]QjQj[ RZCRK RYNRT RW[S[ RSDVGXKYOYVX[',
	    'Poa[d[ RdHd[ R_IlIjL RYDRK RXLWPRT RaD]M R`O]W RiNkW RW[S[ RSDWHYNYVXZ',
	    'Po\\NlN R`[d[ RYKYY R\\H\\L RdNd[ RZDRK RXLWPRT RcCdF R\\HlHlL RW[T[ RUFXJYR',
	    'Po\\ElE R^OjO R^UjU R[ZlZ R^J^U R^JjJjU RZDSK RYNRT RW[S[ RSDWHYNYVX[',
	    'Po]JjJ R]OjO R]E]Z R]EjEjO ReUj[ RZDSK RYNRT RcOfW RjSeU RW[S[ ReY[Z RSDVGXJYNYVX[',
	    'Po[GlG RcCcG RgJkN RZDSK R`J[O RYNRT R_OaTfXl[ RgOeTaX[[ RW[S[ RSDVGXJYNYVX[',
	    'Po^ZiZ R^Q^[ R\\RjRi[ RZDSK R`F[K RXLWORT R_GaJfPlR RaC_H R`FiFgJaO[R RW[S[ RSDVGXJYNYVX[',
	    'Pof[k[ RfCfZ RlWlZ RgOlT R`V[[ RZCRK RYNRT R\\G^L RjGiK R_Q[S RW[S[ RSDWHYNYVX[ RaCaR`W',
	    'PoZHlH R[MkM R]ZjZ R]R][ RcDcM R]RjRj[ RZDSK RYNRT RW[S[ RSDWHYNYVX[',
	    'Po^QeQ R^VeV RS[W[ Re[j[ R^L^X R^LeLeV RZDSK RXNRT R_C[K R]HlHlUj[ RSDVFXJYOYVX[',
	    'PoZKlK RZQlQ Rg[j[ R\\E\\[ RaEa[ ReEe[ R\\EjEj[ RYDRK RWORT RTEWLUQ RW[S[ RWGXNXVW[',
	    'PoZLlL R]ZjZ RYKYY R]R][ RdEdR R]RjRj[ RZDSK RXNRT RW[S[ RSDVFXJYR RjD\\F',
	    'Po[IlI R[PcP RhDkF RgTa[ RZDRK RXNRT RjMfV RX[S[ RfCeMgX RUEXJYOYVX[ R_K_R^X RlVk[i[',
	    'Po[NlN Rb[g[ R\\G\\K RhJh[ R^QaT RZDSK RYNRT RdCdG RW[S[ R\\GlGlK RSDVGXJYNYVX[',
	    'Po`IiI RaUhU Rh[l[ RS[X[ R]E][ RaMaV RaMhMhU R]ElEl[ RZCSJ RYMRT RSDXHYOYVX[',
	    'Po\\GhG R[MlM R`[k[ RaQa[ RbCbM RlXk[ RZCRK RYNRT RkEiI`RZU RiQ`U RW[S[ RSDVGXJYNYVX[',
	    'Po\\RjR R\\I\\Q R\\IjIjQ RZDSK RXNRT RiTl[ RW[S[ RSDVFXJXOXVX[ RcCcY\\Z RfYaY RjXdY',
	    'Po\\GkG R[QlQ RZCSK RYNRT R^J_O RiJgO RW[S[ RSDVGXJYNYVX[ RcCcPbU_Y\\[ RcPfVk[',
	    'PobElE RiWkW RXKXY R[G[S RcJcX RgEg[ RcJkJkW R]WZZ RYERK RWORT RTEXLUR RWZTZ RWHXQ R_D_Q^W',
	    'Po_MhM R\\QkQ R\\ZcZ RfUjZ RcDjK R`T_X\\Z RZDRK RcCbG\\L RYNRT RbR`W RX[T[ ReY_Z RSDWHYNYVXZ RiYcY',
	    'Po_FgF R[OlO R\\TjT R_[c[ RYKYY RcJc[ R\\KjKjU RgGcK RZDRK R_G[K RXLWPRT R`C^I RUEXJYQ RWZT[',
	    'PobKlK RXKXY RZM^M^Y R\\E_H RjEkH RXERK RVPRT RTEWLUR RbW]Y RW[T[ RgDgMjX RgKdY RWHXQ',
	    'PoZ[][ Rc[g[ RXKXY R^K^[ RgCg[ RXERK RVPRT RTDWLUR R[EaE^K R`OYQ RiJlT RdJbU RW[S[ RVGXQ',
	    'Po]KhK R[QkQ R_[b[ RcKc[ RcCiJlK RZDRK RcC[K RXLWPRT RiTjY R]S[X RW[S[ RUEWJXOXVW[',
	    'Po\\HbH R\\TjT RZZlZ R\\D\\O RcPcZ ReDeOlMkO RZDSK RXNRT RjFdI RbMZN RW[S[ RSDVFXJXOXVW[',
	    'Po[JlJ RcZkZ RXKXY RdJdZ RfDiG RlVlZ RYERK RWORT RXLUR R^S[[ RiLcO RjPcS RW[S[ RkTdW_X RWHXR R`D`L^W',
	    'Po[LlL R_P_Y RdNd[ R`NZT RZCRK RYNRT R]DjH RiDbG[I RW[S[ RbI^RkQjXgX RSDVGXJYNYVX[',
	    'Po_JkJ R^RlR R^VlV RS[X[ Rh[l[ R^M^[ R_D_J R_DkDkJ R^MlMl[ RZDTJ RYMRT RSDWGYKYOYUX[',
	    'Po\\JkJ R\\PkP R\\UkU RZZlZ R\\E\\P RdEdZ R\\EkEkP RZDSK RXNRT RW[S[ RSDVFXJXNXVW[',
	    'Po[HkH R^NaQ ReUk[ RhMlQ RaU[Z RZDRK RXNRT RdCcPgX RiJfQ R^J[R RcO_W RW[S[ RcKdT RUEWJXNXVW[',
	    'Po^EjE R[HlH R]LjL R]PjP R]ZjZ R]T][ R]TjTj[ RZDSK RYNRT RW[S[ RSDVGXKYOYVX[',
	    'Po[IjI RgFkJ RaP[V RZDRK R`K\\O RXNRT R_ScXj[ RaC_I RfIfOkNkO RW[S[ R_RhScXZ[ RUEWJXOXVW[',
	    'Po]KjK R]PjP R]F]Z RcCcG R]FjFjP RdUjZ RZDSK RYNRT RcPfW RjSeU RW[S[ RdY[Z RSDVGXJYNYVX[',
	    'Po^JjJ R^PjP R^UjU R^E^U R^EjEjU RhWk[ RZCSK RYNRT R`X[[ RW[S[ RSDVGXJYNYVX[',
	    'PoZIaI RcLlL RZ[^[ RXKXY R^C^[ RiLi[ RYDRK RWORT RTEXLUQ RaOZQ RW[S[ RWGXR RkDcEcRbY',
	    'PogMkM RbVlV Rc[l[ RXKXY RbKbV RgEgV RXERK RVORT RTEWLUR R^R[Z RkDbF RW[T[ R[E_E[M_M]W RWHXQ R[Q]VaYiZ',
	    'PoXKXY RfCfS R[K^L R\\D`E RYERK RVORT RiHlN RTEWLUR R]RZZ RjPhUeY_[ RbH`P RWZTZ RWHXQ',
	    'Po[LdL Ri[l[ RYKYY RaEa[ RgFgU RlDl[ R`MdQ RZDRK RYMRT R`K[U RWZT[ RdD\\E RSDWHYR R`H_O',
	    'Po]IjI R]MjM R]PkP R[UlU R]D]M RdPd[ R]DjDjM RZDSK RYNRT RW[S[ RSDVGXKYOYVX[',
	    'Po_LgL R[ZlZ RYKYY RdElM RZDRL RXLWPRT RbF[M RjPeZ RcCaH R]P_V RX[T[ RcPdU RUEXKYR',
	    'Po[GlG R]QjQ R`[c[ R]K]Q RcCcG RcQc[ R]KjKjQ RhUlY R^UZY RZDSK RXNRT RW[S[ RSDVFWJXNXVW[',
	    'Po[HaH RfHlH RfNlN RfTlT RXKXY RfCf[ R_W[[ RYDRK RWORT RTEXLUQ RaCbTZU RW[S[ RWGXR R[NaN_W',
	    'PoSF\\F RbFkF RTJ\\J RbJkJ RRM\\M RbMlM RRTlT R\\C\\P RbCbP RePhR R^SdYl[ R_Q]VZYR[',
	    'PocFkF Rf[l[ RcCcI RgSg[ RcKcQkPkQ R\\IkIjL RlXl[ RZDSK RXNRT R_Z][ RaT`Y RW[S[ RdM^M RUEWJXOXVW[ R\\I\\T[Z RhLbM',
	    'Po]KkK R]PkP R[UlU R]F]P RfQf[ R]FkFkP RZDRK RYNRT RcCcFdPaS]T RW[S[ RSDVGXKYOYVX[',
	    'Po\\GlG R`LiL R`PiP R[TlT R`C`T RiCiT RgVl[ RSDWH R`V\\[ RYERK RXLVPRT RWZS[ RWIXOXVXZ',
	    'Po]KbK ReKjK R]QjQ Rf[l[ R]E]Q RfQf[ ReEjEkQ RlXl[ R`W\\[ RZCSK RYNRT RaQ_X RcD]E RW[S[ RSDWHYNYVX[',
	    'PoTGjG RSR^R R_RkR RfVj[ R[O]Q RXT]Y RgOiQ RWVR[ RdV_[ RcDfE ReRbY RXRUY RePgX R_FcKkM R_D^IZLTM RXOWW ReOdW',
	    'PoSG`G R`KlK RZCZ[ R]M`P RYQ`W RiEjI RcW_[ RXSRY RgSjZ ReCeRaZ RVKSQ RUMYOWU R^J[P RfOiY RfKcW ReHhV',
	    'PoZGkG RcCc[ R]L`O RcPkY RgMkQ RaRZY RYDRK RXNRT RhJeP R^J[Q RcOaT RW[S[ RUEWJXOXVW[',
	    'Po]GkG R\\MlM R^UjU R^ZjZ R^P^[ R`C`M RgCgM R^PjPj[ RYDRK RXLWPRT RWZS[ RSDWHYNYUXZ',
	    'Po]KlK R`RkR R`VkV Rg[k[ R`N`[ R`NkNk[ R]FlFlJ RcCfF RZDSK RWORT RXLVQ RWZS[ RSDWHXOXVXZ R]F]P[X',
	    'Po\\GkG R]LjL R[PlP Rf[k[ RfPfZ RkWk[ R_D`G RaU[[ RYDRK RXNRT RgDfG RcGcP_X RcMaV RW[S[ RSDVFWJXOXVW[ RcJbT',
	    'Po]IjI R]MjM R]ScS Re[l[ R]D]M R]O][ RfOf[ R]DjDjM RlXl[ RYDRK RXLWPRT RkReT RcZ\\Z RWZTZ RSDVGXJYNYVXZ',
	    'PoSH]H R`KlK RSK]K RRR^R RRV^V RZ[^[ RRNR[ RSDSK RSD]D]K RRN^N^[ RhEkH RgRjZ RdRaZ ReCeNcW ReHgV',
	    'Po]IiI R]NiN R[RlR R]D]N RcDc[ R]DjDjN RcQkY RbS\\Y RYERL RXMRT RWZS[ RSDVGXJYOYUXZ',
	    'Po^LjL R\\QlQ R`[d[ R\\G\\J RdQd[ R\\GlGlJ RhTkW R_U[Y RYERK RWORT RcCdG RSDVGXLVQ RW[S[ RWHYNYVXZ',
	    'Po[K_K R[Q_Q RZ[^[ Rc[g[ RXKXY R[K[Q R[E_E_K RgKg[ RXERK RWMVQRS RdGlGjJ RdCbK RW[T[ RjNlW RcNaW RTDWJXQ R_R^[',
	    'Po^IkI R^MkM R]UlU R]ZlZ R]P][ R^D^M R^DkDkM R]PlPl[ RZDSK RYNRT RW[S[ RSDWHYOYVX[',
	    'Po\\GkG R[OlO R]WdW Re[i[ RXKXY R]R]Y R]RdRdW RiOi[ RYERK RWORT RcIkL RXLUR RcHaK\\M RdCbJ RWZS[ RWHXR',
	    'Po[HeH RZLeL RbXeX RXKXY R[P[X R`C`[ R[PePeX RgFgT RYDRK RVPRT RTEWLUR R\\DZJ RV[S[ RlDl[h[ RVGXQ',
	    'Po^EdE RZPlP R\\UjU R_[c[ RcLc[ R\\LjLjV RZDRK RXNRT RbGcJ RjFkK R]E[K RW[S[ RSDVFXJXOXVW[ RiDaE',
	    'Po^IlI R_NkN R_SkS R_YlY R_G_[ RfHfY RYERK RXLWPRT ReDfG R`D]K RWZS[ RSDVGXKYOXVXZ',
	    'Po[JlJ R_PcP RY[l[ RXKXY R\\R\\Z R`R`Z RcHcP ReReZ R\\RjRjZ RYDRK RWLVPRT R]EiEcG RV[S[ RWGXQ',
	    'Po\\FkF R]IjI R[LlL R^UjU Rf[j[ R^O^[ RdCdL RdOdT R^OjOj[ RZDSK RYNRT RW[S[ RSDVGXKYOYVX[',
	    'Po[GlG RZSlS RcQc[ R_LbO RgLjO RgLdO RZDSK R^L[P RXNRT R_I]O RhIfN RcCcG RW[S[ RSDVFXJXOXVW[',
	    'Po[NkN R]ZjZ R]S][ RcJcS R]SjSj[ RcCiJ R_JhIlJ RZDRK RXNRT RcC\\JZK RW[S[ RUEWJXOXVW[',
	    'Po\\OiO RbJbZ RYDRK RWMVPRS R[D]H RaCbG RiDfJ RV[S[ RVGWRW[S[ R\\J\\U[[ R\\TiT R\\JiJiXk\\mY',
	    'Po]MjM R[TlT R]G]T RaCaT RfCfT R]GjGjT RgVk[ RSDWH RaV\\[ RYDRK RXLVPRT RWZS[ RWIYOXVXZ',
	    'Po]KlK R]D]K RgDgK R]DlDlK RaSeU RaN\\S RZDSK RXNRT RcDbJ`PkOjSeW][ RW[S[ RSDVGXJYOYVX[',
	    'PoZIcI RfKlK RfRlR R[XbX Rh[l[ RXKXY R[P[Z R_C_P R[PbPbX RfDlDl[ RYERK RXLVPRT RWZT[ RWHXQ RfDfSdZ',
	    'Po\\KjK R\\E\\K RjEjK RhOkQ RdGgJ RaP\\U RYDRK RXLWPRT R_M[Q RfMiN R_SbWj[ R`EeG RfDbI_J R_SiSdW`ZZ[ RWZS[ RSDWHYOYVXZ',
	    'Po]NiN R[SjS RS[W[ R[G^J RfHlN RiDfG RZDRK RXLWPRT R`HZN RjGhJ RdDgJ RbRdVgYk[ R\\EaE^J RaV^YZ[ RbNaW RUEXJXNXVW[',
	    'Po]IjI R]MjM R[QlQ R]D]M R^Q^Z RcDcM R]DjDjM RZDSK RXNRT RcQeVhYl[ RkSeV RW[S[ RUEXJXOXVW[ ReY[Z',
	    'Po^SfS R]YcY RXKXY R^L^S RbHbY R^LfLfS RXERK RVORS RlWl[ RTDWLUR RVZS[ RVHXQ R\\DiDiVj[ R\\D\\RZZ RfG^H RfVgZ`Y',
	    'PocIjI R`WgW RS[W[ Ri[l[ R\\N\\[ R_D_N R`R`Y RcIcN R`RgRgW R_DjDjN R\\NlNl[ RZDSK RXNRT RSDWHYNYVX[',
	    'Po]EdE R\\KjK RZOlO RXKXY RhFkI R]WZ[ RYDRK RWORT RcFcJ R`SeXk[ RTEXLUQ R]EZJ R_Q\\Y R_SjSeX^[ R`M]X R`K^V RW[S[ RWGXR RiDaE',
	    'Po]IjI R]MjM R\\RkR R]UkU RZZlZ R]D]M RdNdZ R]DjDjM R^N\\S RZDSK RXLWPRT RW[S[ RUEXJXOXVW[',
	    'Po\\GhG R[LlL R_UjU R_ZjZ RYKYY R`P`[ R^QjPj[ RkEaO RZDRK RYMRT RbCcL`PZR RXZT[ RUEXJYR',
	    'Po[HlH R\\TkT R\\ZkZ R\\N\\[ R`C`K RdNdZ RgCgK R\\NkNk[ RYDRK RXLWPRT RWZS[ RSDWHYOYUXZ',
	    'Po]HlH R]MlM R^SkS R^WkW Rg[k[ RYKYY R]D]L R^O^[ RdDdL R^OkOk[ R]DlDlL RZDRK RXLWPST RX[T[ RTDXIYR',
	    'Po]JhJ R[MlM RT[W[ RXKXY RhVlZ RXERK RVORT RgRjY RTEWLUR RdM[Q RbQZU R`OdSbVYZ R^C^J RkOdReX R^FhFgM RVHXQ RbPeVd[_[',
	    'PoRG_G R`KlK RUQ\\Q RUU\\U R[[_[ RRKR[ RXCXK RXQXZ RRK^K_[ RhDkG RVMVP R[MZO ReCeKdSa[ ReKgRk[',
	    'Po\\JkJ R[MlM Rh[k[ R\\R\\[ R\\D\\J RaRa[ RcCcJ RfRf[ RkDkJ R\\RkRk[ RZDSK RXNRT RcNaS RW[S[ RSDVFXJYNXVX[',
	    'Po[FbF R[IbI R[MbM R[TlT RXKXY R_C_P RdEkEkN RXERK RXMVPRT RcSdWgYl[ RjOgO RbV_YZ[ RcQaX RWZT[ RgEeM RUEWJXQ',
	    'PoaPdP R[SkS RcQc[ ReIeP R[JlJhM RZDRK RXNRT RcShXlY R`GfJ R]EiEcH RcJ`N[O RcS^XZY RX[S[ RUEXJYOYVX[',
	    'Po^HjH R^MjM R]YhY RS[W[ Rh[k[ R]S]Z R^D^M R^DjDjM RcSgW R`NZS RcS`W RZDSK RXNRT R^QlQlVk[ RSDVGXJYOYVX[',
	    'Po\\KbK ReLkL R\\PbP R\\UbU RXKXY R\\E\\[ R\\EbEbK ReEkEkK RgVl[ RYDRK RWORT RePhY RTEXLUR RdPlPiVeYb[ RW[S[ RWGXQ',
	    'Po_JlJ RaOkO R_TlT RXKXY R^G^[ RaEiEiJ RgWk[ RdV`[ RYERK RWLVPRT ReRhY R_C[K RbL_R RWZT[ RTEWJXQ RePdW',
	    'Po]EdE R]LlL R^UlU RXKXY R]E^R^[ RcPc[ RhPh[ RZDRK RXLVPRT R]HlHlM RW[S[ R^PlPl[j[ RSDWGXR R]N[Y RkCbE',
	    'Po[HlH RfRkR R\\VkV R\\ZkZ R\\L\\[ RaHaP RfHfQ R\\LkLk[ R^C_G RZDSK RXNRT RiCgG RaP]R RW[S[ RSDVFXJXNXVX[',
	    'PoUJ_J R`KlK R[Q_Q RTU_U RTY_Y RTMT[ RWIWQ R[I[Q RTM_M_Z RhDkG RWE\\D_J RhSk[ RdRa[ RUESJ ReCfPjY ReHiW ReKdV',
	    'Po^JlJ R`QlQ R`UlU R`ZlZ R`M`[ ReDeJ R`MlMl[ R^DlDlJ RZDSK RWOST RUEXLVQ RWZT[ RWHYNYVXZ R^D]Q\\X',
	    'Po[GkG R]SiS R]WiW R[ZlZ R]O]V RcCcM R]OiOiV RZDSK RXNRT RcFhKlM RcG^LZM RW[S[ RSDVFXJXOXVW[',
	    'Po^PjP RZTlT R]ZjZ RXKXY R]V][ RcPcZ RkVk[ R_N\\R RYDRK RWORT R]HbK R`GfJ RTEXLUQ RbEZI RaFjFgJaLZN RW[S[ RWGXR',
	    'Po`QkQ RZ[\\[ R]J][ RfQf[ RaJeN RgKaQ RXERK RVPRT ReEdI RiTlY RjNlR RbT`Z RWLTR RZE`E]J R`NYP RV[S[ RTDVIWNWVV[ RkDaE',
	    'Po\\FlF R]LkL R]OkO RhRkR R[TlT Rc[g[ R]I]Q RdCdQ RgSg[ R]IkIkQ R^VaY RYDRK RXLWPRT RfDkE RWZS[ RSDVGXJYNYVXZ',
	    'Po^MjM R^PjP R[TlT R\\E[H R^I^P R^IjIjP R\\ElElH RgWl[ RSDVG R`W\\[ RZDSK RXNRT RcPdT RW[S[ RWHXOXVW[',
	    'PocGiG R_RiR R_WiW Rf[i[ R^D^K R_O_[ RcGcK R_OiOi[ R^DiDiK RZDSK RYNRT R[K[O R[KlKlO RSDWGXKYOYVX[S[',
	    'Po\\FjF R[JkJ R]QjQ R]M]Q R_U_Z RcCcJ R]MjMjQ RZDRK RXNRT RcQeViZl[ RbS[V RkTeV RW[S[ RUEWJXNXVW[ ReY[[',
	    'PofJjJ R\\JaJ R\\NjN RS[W[ R]F]N RcCcR RfFjFkN RYERK RXLWPRT RaE\\F R^SaWeYl[ R\\SjSfWbYZ[ RUEXJYOYVXZ',
	    'Po]ElE R]IjI R]MkM Rf[k[ R`T`Y ReEeR RgTjW RYDRK RXLVPRT RdTeX R]TZZ RW[S[ R]E]RlRk[ RUEWJXNXVW[',
	    'Po\\ElE R_MkM R_QkQ Rb[e[ RXKXY R_I_Q ReQe[ R_IkIkQ RXDRK RVPRT RjUlY RTEWLTR ReEdI R`T^Y RV[S[ RVGXQ R\\E\\P[Y',
	    'PoSI^I R`KlK RRL_L RTT^T RSDSI RTOTS RUUVY RYCYI RTO^O^S R_D_I RhElH RcW_[ RgRkZ RfCeRbY R\\UZY RfKdV RfIhV R_YSZ',
	    'PoZDaD RZHbH RbKlK R[LaL R[OaO RZXaX RZSZZ RZSaSaX RjEkH RXERK RgDgKeScZ RSDVJWQV[S[ RVMSS RgKiSl[',
	    'PobElE RZM`M RiWkW RZX`X RXKXY RZGZZ RZG`G`M RZQ`Q`X RcJcW RgEg[ RcJkJlW RYDRK RVPRT RTDWLUR R]C\\G RV[S[ RVGXQ',
	    'Po]IiI R]MiM R[QlQ R^WiW R]F]M RcSc[ R]FiFiM RSDWH RfQjU RbP\\V RZDRK RXNRT RW[S[ RWHXOXVW[',
	    'Po[GkG R]LjL R[PlP ReWjW RdZlZ RcGcPdZ RiTiW RlWkZ R_DaG R`TZ[ RYERK RWORT RgDfG RgRfW RSDVGXLVQ RbO_W RW[S[ RcKaT RWIYOXVX[',
	    'Po[JkJ RY[l[ RXKXY R\\R\\Z RaRaZ ReReZ R\\RiRiZ ReLhMkP RZDRL RWORT RhDgG R^D_H RUEXLUR RaL^O[P RW[T[ RWHXR',
	    'Po\\FkF R]JjJ R[MlM R\\TkT RXKXY RdPd[ RfMkR RaM[R RYERK RVORT RbTiW RTEWLUR RcG_O RcT_X[Z RcCaM RWZT[ RfP_Q RWHXQ',
	    'Po]OiO R]RiR R^ZjZ R^L^[ R]LiLiR R]VjUj[ RbHdJ RcDjL RYERK RXLWPRT RcE\\L RWZS[ RUFXJYNYVXZ',
	    'Po]HjH R]KjK R]NjN R[WlW R]F]N RbCbF RcOc[ R]FjFjN RYERK RXLVPRT ReSkT RkQeQ RaS\\T RWZS[ R\\PaQ RWHXNXVXZ',
	    'Po^GjG R^JjJ R\\QkQ R^D^J R^DjDjJ R\\MkMkQ RYDRK RXLWPRT RaMaQ RfMfQ R^SbXfYl[ R\\M\\Q RWZS[ R\\TjTgWcYZ[ RSDWHYOYVXZ',
	    'Po\\FkF R[JlJ R]QjQ R]TjT Rf[l[ R]M]T RdCcF RgTg[ R]MjMjT RlXl[ RZDSK RYNRT R_G`J RhGgJ RaT_X^Z[[ RW[S[ RSDVGXJYNYVX[',
	    'PoaEjE RTIYI RSLYL R\\LlL RSUlU RTDTH RYCYQ R_NaP R^GbH RiFaJ RdC\\G RcSjU RULTR RaEfH]K RgJgQcQ R_U\\XR[ R_UcYl[ R_R^W',
	    'Po[FlF R]MjM R]QjQ R[UlU R]J]Q R`C`H RgCgH R]JjJkQ RZDSK RXNRT RdVgYl[ RbV`Y[[ RcQbX RW[S[ RSDVFWJXNXVW[',
	    'Po\\FkF R[JlJ R]PjP R]SjS R[VlV R]M]S RcCcF RcSc[ R]MjMjS RZDSK RYNRT R_F_J RgFfJ RW[S[ RSDVGXJYNYVX[',
	    'PoZFcF R[NbN R[SbS RZVcV RXKXY R[J[S R_C_[ R[JbJbS ReLkLj[ RYERK RVORS RWMUR RVZTZ RTDWJXQ RlEeFeScZ',
	    'PoTE^E RbGlG RRH_H RSK^K RRUlU RWMRR RbGdKgOlP RcRhS RYCXKUQ RcCaJ RiGhKeN`P R_TcYl[ RVN]N\\QYQ R_U[YS[',
	    'PoZGcG ReIlI RZKbK RYObO R][a[ RXKXY RhVl[ RhVc[ RXERK RWLVPRT RfQjY RiRfY RWZT[ RfCdLgV R^D]O[Y RTEWJXQ R\\SbSa[ RjJhV',
	    'Po\\JjJ R\\NjN R\\PaQ R[WlW R\\F\\N RbCbF RcOc[ R\\FjFjN RYERK RWORT RSDVGXLVQ RjPeQ RfSkT RaS[T RWZS[ RWIYOXVXZ',
	    'Po]IkI R]OlO R]RkR R]VkV R]ZlZ R]D]I R^M^[ RdCdI ReNeZ RkDkI RSDWH RYDRK RXLVPRT RdKeN R_K\\Q RWZS[ RWIYOXVXZ',
	    'Po\\FkF R[JlJ R]PjP R]TjT R]WjW R]M]W R_H_L RcCcH RhHhL R]MjMjW RZDSK RXNRT RfXl[ R`XZ[ RW[S[ RSDVFXJXOXVW[',
	    'PoZEkE R\\KdK R]TcT RWKWZ R]M]U RcMcU RWFRJ R]G^J RiKhTk[ RgTdZ RWLTS RbFbJ RfKkKkP RV[S[ RfGeP RSDVIWQ RZEZSY[ R`K`T_Y RhOgW',
	    'Po\\Q^Q RRUlU RTGTQ RYCYP RSH^H^P R\\D_G RVERH R[J\\M RbGdKgOlP RdRgS RVJVN RdC`J RbGjGhKdN`P R_R^V[YS[ R_TdYl[',
	    'PoWJWY RZKZ[ R^C^[ RWFRK R[E[J RgRkZ RiQeZ RWMTR RaE`I RjLgX RVZSZ R`N`U RfGfLhW RfIkIhV R\\N[U RfCdN RZKbKb[`[ RVGWP',
	    'Po\\FlF R[JlJ R]RjR R]VjV RXKXY R]N]V R`C`J RcJcV RgCgJ R]NjNjV RYERK RWLVPRT RfXkZ RaX[[ RWZTZ RTEWJXQ',
	    'Po[IlI R\\NkN R]QjQ R[UlU RaCaI RfCfI RjDiG RYDRK RYNRT R_J`M R]D^G RdWl[ RhJfN RcNcU`Y[[ RXZS[ RSDWGYNYUXZ',
	    'Po[HkH RdRlR RbWlW RXKXY RcCcN RdRdW RiNi[ R]D_G RiDfG R[S_V[[ RXERK RVQRT RcHfKkM RTEWLTS R]O[U R]RaQ^X RcH^LZN RV[S[ RVGXQ',
	    'PofSlS R`YgY Rh[l[ RXKXY R[O[[ R`U`Z RdIdN RfOfS R[OlOl[ R[IlIiL RYDRK RVPRT R`UgUgY RTDWLUR R^DiDdG RbJ\\M RbO`S]T R`FiI RV[S[ RVGXQ',
	    'Po\\FkF RZJlJ R\\QjQ R\\TjT R\\WjW RZ[l[ R\\M\\T RcCcF RcMc[ R\\MjMjT RYDRK RXNRT R_G_J RgGfI RW[S[ RUEXJXOXVW[',
	    'Po]FjF R[IlI R[MbM RdMkM RcQlQ RZTlT Re[l[ R_K_Q RcCcI RfTf[ RgKgQ RlXl[ RYDRK RXNRT R`T^YZ[ RW[S[ RbP[Q RUEWJXNXVW[',
	    'Po[FlF R]PiP R]SiS R`[c[ RXKXY R^L^S RcSc[ R\\MiMiS RgVkZ R[H^K ReHkN RjHhK R^VZZ RYDRK RWLVPRT R`IZO RbC_K RV[S[ RTEWIXR',
	    'Po\\IbI ReIjI R\\NjN Rf[k[ R]E]N R`UaY ReEjEjN RdUeX RgTiW RaN[T RZDRK RXNRT R]U[Z RbD\\E RW[S[ R]RlRk[ RSDVFXJXOXVW[',
	    'Po[HkH R_OgO R`XgX Rg[k[ R\\Q\\[ R`J`O R`T`Z R`TgTgX R^KhKhO R\\QkQk[ RfHkM RZDRK RXLWPRT RaH\\M R\\EcE_K RW[S[ RSDVGXJYOYVX[ RiDbE',
	    'Po`KkK RSK_K RSO_O RRR`R RTY^Y RSHSO RTTT[ RYHYO RTT^T^Z RSH_H_O RSDTG RhEkH RWCXF R^D\\H ReCeKeSaZ ReKgSkZ',
	    'PoZGcG R[MbM R[[^[ RXKXY R[I[M R_S_[ R[IbIbM R^C_G R[QbP_S RhVb[ RXERK RVORT RfRjZ RiPfY RTEWLUR RWZT[ ReJhW ReJkIhV RfCdO RcUYV RWHXQ',
	    'Po[JbJ R[NbN RZQcQ R^[a[ RXKXY R[F[N R]C]F R[FbFbN RgWc[ RYERK RWLVPRT RfRjZ RiQeY ReLhX RjLgW RWZTZ ReJgV ReJkIhV R^O^Q[Y RfCdN R]UbUa[ RTEWJXQ',
	    'Po[FlF R]LjL R]OjO R]YhY Rf[j[ R]H]O R]S]Z R`C`G RgCgG R]HjHjO RbUfW R`O[T RcS_W RZDSK RXNRT RW[S[ R^RkRj[ RSDVFXJYOYVX[',
	    'Po\\IjI RZLlL R]SjS R\\D\\I R]N]S R`U`Z RaDaI RfDfI R]NjNjS R\\DjDjI RZDRK RXNRT RdSeVhYl[ RdSZW RkUfW RW[S[ ReY\\[ RUEWJXNXVW[',
	    'Po\\JlJ R]UgU R_YhY R\\YdY Rg[k[ RXKXY R\\D\\J R]P]U RaDaJ RbNbY RfDfJ R]PgPgU R\\DlDlJ RfVhY RYDRK RXLVPRT R^KZP RW[S[ RUEWJXR R]NlMlUk[',
	    'PocEjE RZIlI R[MlM R]PdP RXKXY R^D^I RcCcI R[M\\T\\V RaPbW^Z RhTb[ RXERK RVPRT RgJjK RcSaX RjPgW RWLUR RV[S[ RlXk[i[ RTDWIXQ ReKgX R[OZY',
	    'Po\\QjQ R]WiW R]ZiZ R\\L\\Q R]T][ RcLcQ R]TiTi[ R\\LjLjQ RdCiH RgNeP RZDSK RbC\\J RXNRT R_N_P R_IhIlJ RW[S[ RUEWJXOXVW[',
	    'Po_JgJ R[SaS RdSjS RXKXY R[M[S R[MaMaS RdMdS RdMjMjS RgVl[ R^VaY R^WZ[ RfXc[ RZDSK RXNRT RcChHlJ RgTeZ R^T\\Z RbC]IZJ RSDVFWJXP RW[S[',
	    'Po[ObO RdQlQ R[TbT RcUlU R_[b[ RWKWY R^J^S RZJbJb[ RhMh[ RXERK RVPRT RWLTR R\\FaF^L RgFeK ReMcS RkKhK RV[S[ RTDVIWQ RdEkEkL R]CZJZTY[',
	    'PoZGbG RZQbQ RXKXY RZKZQ R^C^[ RZKbKbQ RdJdU RdJkJkU RhWl[ R]QaV RgWc[ RWERJ RVPRT ReFjFhJ RXCXF RWLUR R^P[X RfCcJ RV[S[ RTDWIWQ RhMhSgX',
	    'Po[ElE Rh[k[ RXKXY R[J[[ RcEc[ R[JkJk[ R^MaP R^TaW ReTiW ReMiP RhFkI RaM]Q RaT]X RhLdQ RhTdX RYDRK R^F[I RXLVPRT RW[S[ RUEWJXR',
	    'PoZGlG R\\PjP R\\SkS RZVlV R\\J\\P R`X`[ R[EcEdU R\\JjJjP RhWl[ RdWe[ R\\WZ[ RXERK RWORT R_L`N RfLfN RUFXLUQ RWZS[ RWGXNXVWZ RiCbD',
	    'Po`LgL RZRlR R[UlU R`[c[ R\\N\\R R`H`L RaNaR RcCcF RcUc[ ReNeR R\\NjNjR R[FkFkH RhIkL RYDRK RWORT R[F\\I[L RaGdH RTEXLUQ RW[S[ RXKXWW[ RWGXR',
	    'Po^IiI R^RjR R^UjU R^XjX R[F[I R^O^X RcCcF RcIcM RZOjOjX R[FlFlI RZDSK RXNRT RfYl[ R`K\\L RaYZ[ RhKbNZO RW[S[ RSDWGXNXVW[',
	    'PoZElE R\\JkJ R\\MjM R\\OjO R\\RlR R]I]R R_C_G RdHdR RfCfG RYDRK R^WcYj[ R\\UiUcY[[ RTEWLXVV[S[ RWLSS R]H[K',
	    'Po\\EkE R]JbJ R[PlP Ri[k[ R\\H\\K R\\T\\[ RbSb[ RdEdN RfTf[ R\\TkTk[ R\\HlHlK RYERK RYNRT RjJfJ RfLkM RaL\\M RXZS[ RbPaW RUEXJYNYVXZ',
	    'Po[FlF R_LhL R[OlO R\\J\\L R`C`H RgCgH R\\IkJkL RhVlZ RgSiX RYERK RWORT RXLVQ RdO\\S RcS[V RkQdUZZ Rd[`[ RcSeWdZ RW[S[ RSDWHYNXVXZ',
	    'Po\\PjP RXKXY R\\J\\P R\\R\\Z RcRcZ R\\JjJkP R[F^I RaFdI RhFjI RjCgF RYDRK R^C[F RWORT RdCaF ReSgT ReVgV R^SaS R^VaV RlYl[ RTEXLUQ R_LhN RfL^N RW[S[ R`YYZ RhYaZ RiSj[ RWGXQ',
	    'Po\\DlD R^IiI R^LiL R`SgS R_VhV RXKXY R[N[Q RaPaS RcDbG R^GiGiN R^G\\NlNlQ RhPkS R]Q[S RYERK RXNRT R`U[W R`WdYk[ RWZT[ RgWcY\\[ RUEXJXQ',
	    'Po[FlF R[IlI R`LhL R[NlN R^TjT RWKWY R`F`L RdCdF RdNdW RhFhL R^QjQjW RXERK RVPRT R^Q^WiXl[ RWLTR RaY\\[ RV[S[ RTDWIWQ R[F[RZY',
	    'PoSHXH RZH_H R`KlK RSM_M RSP_P RRS`S RTZ^Z RSJSP RTUT[ RSDXDXH RYJYP RTU^U^Z RSJ_J_P RhEkH RgRk[ RdRa[ RSDSH RZDZH RZD_D_H ReCeMhW ReKdV',
	    'PocEjE RbMjM R_QjQ R_TjT R\\[l[ RXKXY R_N_T RbVb[ RcCcL RdNdT RfVf[ R_NjNjT R^VjVj[ RXERK RVPRT R\\HlHkJ RWMUR R^V^[ RV[S[ RTDWIXQ R\\H\\RZZ RhJdJ_J',
	    'PoYGbG RdPkP RZQbQ RdSkS RWKWY RZKZQ R^C^[ RZKbKbQ RdLkLkW RdLdWjWl[ R]RbV ReXb[ RXERK RVPRT RWLTR R^OZX RgDdK RcDkDkJhJ RV[S[ RVGWQ',
	    'PoYE_E RVJ]J RaKlK RYL_L RUN_N RWR^R R\\V_V RXX]X R][_[ RVTV[ RYCYG RZXZ[ R[T[V RWPWQ^P^R RVT_T_[ RTG`G`I RiDkG RZTWW RhSk[ RYHYL ReSa[ RfCfMiX RTGTTRZ RfKdV',
	    'Po_DlD RWJWZ R]P]Z R`J`[ ReDe[ RgMiP RiFlI RVFRJ RcRbW RiLgP RiRgW RWCUH RWMTS RcLbP R\\[Z[ RZJ^J RZKYP^P RYD]D]KZJ RV[S[ RbF`IkJk[i[ RSDVIWQ',
	    'Po[FlF ReLjL R[NkN R]QjQ R[TlT R_W^[ R\\LaL`T R\\HbHbL ReHfLfT ReHjHjL RYERK RXLVPRT RcCcF RdUgYl[ RdTZX RjVfX R\\H]L RW[T[ RdY^[ RWHXOXVW[',
	    'Po[FlF R\\MbM ReMkM R]QlQ R]TkT R]WkW R]ZlZ R^P^[ R`C`G ReOeZ RgCgG R^PZT RYDRK RXLWPRT ReIkIkM R\\I]M R\\IbIbM ReIeM RW[T[ RSDWHYOYVXZ',
	    'Po[DlD RaJeJ RaLeL RaOeO RXKXY R[I[P RaGaO RaGeGeO RYDRK RVPRT RfQkS RTDWLUR RcEbG R]G^OZO R`Q[S R`T[W R_UaXeYk[ RhGkGkKgK RV[S[ R_UhUeXbYZ[ RVGXQ R]J_J RhGhOkNkO',
	    'Po[IkI RcNlN RcRkR RcVkV RcZlZ RXKXY R[D[I R^S^[ R`E`I RdMd[ RfDfI RhMhZ R[DkDkI RZSaRaT R_M^P RZM^P[S RXDRK RVPRT R]J[N RaUaY RWLTR R[VYZ RV[S[ RTDWIWQ RdKcR',
	    'Po[HbH ReHkH RZJlJ R]MdM R]ScS R]UcU R[YcY RWKWY R[D[H R]P]Y R`M`P R[DbDbH R\\PcPc[ ReDeH ReDkDkH RhWl[ RhWe[ RXERK RUPRT RfQjZ RjRgY RWLTR RfPkOhX RgLeT RV[S[ RTDVIWQ RZJZSY[',
	    'PoYFdF RZL]L R_LcL RZRcR RZUcU RYWcW RWKWY RZIZM RZNZU R\\C\\H R_N_[ RaCaH RZNcNcU R_IcIcM RfLkLj[ RXERJ RWLTS R]I]M R]IZI RV[S[ RVGWQ RlEfFfSdZ',
	    'Po[FbF R[JbJ RdJjJ R]OiO R]QjQ R]TkT RXKXY R]L]S RdLdS RdDjDjJ RYDRK RWORT R]J]MZO RTEXLUR R^HbH R[D\\I_H[H R[VjVeY^[ RdFjGgHdHhH R^WcYl[ R[DbDaI^H RW[S[ RfY[[ RWGXR RdDdJcQ',
	    'PoRHlH RVZjY RgVl[ R_C_G\\JWO ReKWZ RWO]S',
	    'PoRH_H RbSeS RVX^X RTX\\X ReCeS RUNXQWUSX R[S^Y RiHlN RYCYGVM R[LYQ RaH^O RjOhUeX^[ RWXRX',
	    'PoRH^H R`HlH RRY]Y R_YkY R_ObPeScV`Y RTNXSVVSY R[U^[ RgUjWl[ RgNfS RiLgQ RYCXGUN RZOXS R\\LYR ReCeHaO ReGbM',
	    'PoSGkG RYQdQ RRUlU R_R_[ RZK^M RaNeR R_C^H[K RbJ[Q ReOlR RTJXK RYORQ RjIeK',
	    'Po`HlH RRH_H RaLaZ RTOXSWV RVVSZ RkOgRkZ RYCYHUN R\\MYR RbC^K RfMiX RjJaM RgY_Z RXYRZ R[U]ZWY',
	    'PoTEjE RUPiP RR[l[ R_E_[ RdSiX',
	    'PoTEjE RUPiP RR[l[ R_E_[ RcHhM',
	    'PoTFjF RUPjP RR[l[ R_F_[',
	    'PoRF\\F RRN\\N Ra[k[ RWFWW RaDa[ RlVk[ R]URW',
	    'PoVIkI RTRjR RR[l[ R_H_[ RXCUL',
	    'PoRE[E R]FlF RSM[M R`[f[ RWEWW RfFf[ R\\VRX',
	    'PoRE[E RSMZM Re[j[ RWEWW R`UZ[ RbQ^Y R[URX R]IlIlSj[ RcCcLaU',
	    'PoRF[F RSNZN RVFVV R^SZ[ R_N\\Y R[URW R_FgFgKiX R_I]W',
	    'PoRE[E RSM[M Rh[l[ RWEWW R`EhEi[ RlUl[ R`E_T\\[ R[URX R`N^X',
	    'PoRF[F R]FkF RSN[N R[[l[ RWFWW RdFd[ R\\URW',
	    'PoRE[E RSM[M R]PhP Rd[j[ RWEWV RaC]M R\\TRV R_HlHlSj[',
	    'PoRF[F RSM[M RWFWU ReEeX RlDl[ R[TRW R^D^Q]X',
	    'Po]EkE RRE[E RSM[M R\\OlO RWEWV RdEd[ R[URV',
	    'PoRF[F RSNZN RWFWV RhWl[ RcS[[ RfQjY R`F\\P RfJaU ReNhW RaC^M R`HgHdR R[URW',
	    'Po]EkE RRE[E RSM[M R\\NlN R_[d[ RWEWV RdEd[ R[URV',
	    'PoRF[F RSN[N R^OjO R_[k[ RWFWW R_O_[ R^EjEjP RlWk[ R[URX',
	    'PoRE[E R`HlH RSM[M R]TiT Re[i[ RWEWW R[URX R`C^OlOj[',
	    'PoREZE RSMYM Re[i[ RVEVV RbO\\U RcLaQ RZTRX RhLdU^[\\[ R]EhEdHcI]LlMjZ',
	    'PoREZE RSNZN R\\UiU Re[j[ RVEVW RZVRX R^PlPj[ R_I^P R^EiEhP',
	    'PoaEgE RRF[F RSMZM RdYjY R`YfY R\\ZcZ RWFWV RhTk[ RhFlO R`F\\O RbM_Z R[URW',
	    'PoRF[F R`HkH RSN[N R]PlP RWFWW ReHe[ RaC]L R\\URW',
	    'PoRF[F R]IkI RSNZN R\\PlP RWFWW RfVk[ RcDcQhX RdPaV^YZ[ R[URW RcLbU',
	    'PoRF[F RSNZN RWFWW RdCdH R_LcUgYl[ RhLeTaX[[ R_HbS R\\HjHfS R[URW',
	    'PoRE[E RSMZM RWEWV RgNg[ RfHlM R_VZ[ RaH[N RdCfI RcC`J R[TRV R`N`R_W',
	    'Po]EkE RRE[E RSNYN R^OlO Rb[g[ RVEVW RhEh[ RhN`VZY R[VRX R_G]N',
	    'PoRF[F RSMZM Rc[g[ RWFWU R`V[[ RaEgEkM R_G]M RbO`W R[URV R]OiOiTh[',
	    'PoRE[E R\\IlI RSNYN RVEVW RcIc[ RcEdI RcNiP R[VRW',
	    'PoRE[E R\\IcI RSMYM R\\NkN RZTlT RVEVW RcCc[ RZVRW RjG`H',
	    'PoRE]E R`KlK RSM\\M R`SlS Rg[l[ RXEXU R`ElEl[ R^WZ[ R`E`S]Y R]SRW R`M_W',
	    'PoRF[F RSNZN R[PlP RWFWW R\\HiHiP ReUk[ RaU\\Z RcCcPgX RcP_X R[URW RcLbU',
	    'PoRF[F R^LkL R\\LhL RSNZN Rf[k[ RVFVV RfOfZ RlWk[ RfGkK R^VZ[ RbD^L R[TRW R`O^X',
	    'Po^FkF RRF\\F RSO\\O R^PkP R[[l[ RWFWX RdFd[ R\\WRY',
	    'Po]EjE RRE[E R\\MlM RSNZN RWEWV R[URW RaM_W[[ RgMg[k[mX',
	    'PoRE[E R_LhL RSMZM RWEWV RcCkL RbE[L R\\QjQe[ R[TRV',
	    'PoRF[F R_IlI RSNZN RWFWW R`McUgYl[ R`F\\O RhKdT`XZ[ R_IbT R`C]N RhIfR R[URW',
	    'PoRF[F RSNZNlO Re[i[ RVFVV R^EiEi[ RaHeK R^E^SZZ R[URW R^M]W',
	    'PoREZE R]LcL RSMZM Re[l[ RVEVU R]C]Z ReCe[ RlWl[ RkJiLeN RZTRV RbYZZ',
	    'PoRE[E R]GkG R^LjL RSMZM RWEWW RdCd[ RkXgX R[URW R\\QlQkX',
	    'Po\\ElE RREZE RRMYM RVEVW RdHd[ ReLlR RbL\\R ReEaN RYURW',
	    'PoRE[E RSMZM ReZkZ RWEWW R^E^R ReReZ R^EiEiR RbU]Z RlVkZ R[URX RcHcPbU',
	    'PoRE[E RSMYM RbUgU R_[j[ RVEVW R_N_[ RcDkL RkWk[ RcE[L R[VRW R^NhNgU',
	    'PoRF[F R_LhL RSMZM ReYiY RWFWV RbPb[ R\\PjPjX RcCkL RbE[L R[URV',
	    'PoREZE RRMYM RVEVU R^H^[ RhDlG R`C[N RZTRV RlWk[i[ RfCfNgY ReL`M RhLcL RlLeL',
	    'PoRF[F R[KlK RSNZN RcUiU R^[l[ RWFWX R^D^[ RcCcU RiDiU R[VRY',
	    'PoRF[F R[IlI RSN[N R^QiQ R^ZiZ RWFWW R^C^[ RiCi[ R[VRW',
	    'PoREZE R]EkE RSMYM R\\QlQ RVEVW RdEd[ R_H`N RiHhN R[URW',
	    'PoRE[E RcIlI RRMZM R]ZjZ RVEVW R]P][ RcCcP R]PjPj[ R[URX',
	    'PoRFZF RRNZN R^SfS RfVjV R^[k[ RVFVV R_L_[ R^LfLfS RlWk[ R`C\\L R[URW R_HkHkPjV',
	    'PoRF[GlH RSNZN R^ZjZ R\\ZgZ RVFVV RjWk[ RfTjX RePcS RaU]Z RgLeP RcCbH`L]NbSaV R[URW',
	    'Po[P_P RTRjR RUViV RR[l[ R_R_[ R_F_O ReJiO RZCTI RZJUO RWGjGgI RdXhZ',
	    'PoREZE RRMYM RVEVV RdCdN R_NaTeXl[ R\\HkHjL R\\NjNgTcX\\[ RZURW R\\H\\R[Y',
	    'PoREZE R`MdM RRMZM Rf[l[ RVEVV R\\J\\[ R`D`Z RfDf[ RlWk[ RkKfM RZURW RdYYZ',
	    'PoRF[F RSN[N R_UfU Re[j[ RWFWW R_M_W R_MfMfU R`C]L R\\URW R_IlHlTj[',
	    'PoRF[F R]LlL RRNZN R]SlS RWFWV R]E]T ReEe[ R]ElElT R[URW',
	    'PoRE[E RSM[M RfNkN R^ZkZ RWEWV R^Q^[ R^QkQk[ RcJ^N RdEaL R\\TRV R^ElEkN',
	    'PoRE[E RSMZM R^QlQ R^ZlZ RWEWW R^H^[ R^HlHl[ ReCcH RZURX',
	    'Po\\ElE RREZE RSNYN Rh[l[ RVEVW R`KhKh[ RbOeT RZURX ReY]Z RlWl[ R`E`L`O_R_U^[',
	    'PoRE[E RRN[N R^TeT Rd[i[ RVEVW R^J^U R^JeJeS R\\EkEj[ R[VRX',
	    'PoRE[E RSMYM R^NjN R[TlT Rf[j[ RVEVW R^H^[ RdCdT R^HjHk[ RZURW',
	    'PoRG[G R_JkJ RSO[O R^RkR R\\[l[ RWGWX RdDd[ R`E]N R\\WRX',
	    'PoRF[F R]MlM RSMZM R]SlS RWFWV R]G]U RdCd[ R]GlGlT R[URV',
	    'PoRE[E RSMZM R`MkM R\\MiM R^ZjZ RWEWV R^Q^[ R^QjQj[ RfHkM RbC^M R[TRW',
	    'PoRF[F R]LiL RSNZN R]TiT RY[l[ RVFVW R]E][ R]EiEi[ R[VRW',
	    'PoREYE RSMYM RfXlX RVEVW RfGfZ RfGlGlZ RYVRW RaZ^Z R^C^N\\X R[IcIcSaZ',
	    'PoRF[F R]JjJ RSNZN R]PlP RVFVW R]D]Z R]DjDjJ R[URW RlWk[i[ RdX[Z RdLfX',
	    'PoRFZF RSMZNlO Rh[j[ RVEVV R\\E\\[ RaEa[ ReEe[ R\\EjEj[ RZURW',
	    'PoRE[E RSLZL RhVlV RVEVT R\\F\\U RfFf[ RfFlFlU RaW\\[ RcD\\G RZSRU RbGbT[U RbNaW',
	    'PoRF[F RSN[N Ra[i[ RVFVV RaIa[ RcR[[ R`DeF RiWi[ RiFcS RjLlT R]L\\S RZURW',
	    'PoRF[F RSMZM RVFVV RcCkK RbD[L RdKaM]O RfOcS^U RjSfW_[ R[TRW',
	    'PoRF[F R^IlI RSNZN Ra[d[ RWFWV RdHd[ R`C\\M RiNlW R_O[VRW',
	    'Po^FkF RRF\\F RRO[O R^PkP R\\[l[ RWFWX RdFd[ RhSjX R\\WRY',
	    'PoRE[E R]IkI RSMZM R\\OlO RVEVW R`U\\Y RgTjWl[ RdCdO`V R[URW RdY\\Z RjXcY',
	    'PoREZE R[JlJ RRMZM Re[l[ RVEVV ReJeZ RlXl[ ReEiF R^WZ[ RjNhQ`Y R`R\\Y RZURW RaCaL_U',
	    'PoRFZF R[JlJ RSNZN R]OiO R\\SjS R[WkW RVFVV RcCc[ R]FiFiO RZURW',
	    'PoRF[F R\\HlH RSNZN RWFWW RdCdG RgJlO R`K\\O R`ObTfYl[ RgOeTaX[[ R[VRW',
	    'PoREZE R\\HlH RSMYM RaSlS Rc[f[ RVEVW R_L_[ RgPg[ RbLkLfP R_LZQ RbC]N RZVRW',
	    'PoRF[F ReMjM RSN[N Rh[k[ RWFWV R\\QcQcZ ReQlQlZ RcI^M Rb[_[ RdCbK R\\URW R]FkFjM R_O^Y RhOfY',
	    'PoRFZF RSNZN R[NlN Rf[l[ RVFVW RcCcN RfNf[ RlWk[ R_V[[ R]E_K RiEhK RZURX R`N_X',
	    'PoRF[F R^IjI RRNZN R\\OlO Rf[l[ RVFVV RdCdO RgOg[ RlXk[ R_W[[ R`D\\M R[URW RaO_X',
	    'PoRE[E RSMZM R\\OlO Re[i[ RVEVW R_GiGi[ RdSeV RcJdM RcCbG R[URW R_G_R]Y',
	    'PoREZE R\\JlJ RSMZM R[SlS RVEVW R_D_R RgDgR RgVl[ R`V[[ RZURX',
	    'PoRE[E R_LjL RSMZM R]ZlZ RVEVW R]D][ R]DlDl[ ReGeOgRiW RdOaV R[URX RdJcS',
	    'PoRFZGlH RRMZM R^QjQ R^UjU Re[j[ RVFVV R_K_[ R^MjLj[ R_LZQ RbC]N R[URW',
	    'PoRG[G R]IlI R]NkN RSOZO R\\UlU RVGVX RdId[ R_DaH RiChH R[VRX',
	    'PoREZE RRMYM R^[b[ Rh[l[ RVEVU R\\DbDb[ ReDlDl[ R`Q[V RjHeM RjQeV R`H\\M RZTRV',
	    'Po`GiG RRF[F RSNZN R^ZiZ RVFVW R^R^[ R^RiRi[ RaC`G[K R`GaJeOlQ RhHbO[R R[URW',
	    'PoRFYF R^IgI RSNXN RVFVW RaRcS^Y RaNfNbU RbI_Q RlWl[ RYVRW R\\EiEiTj[ R\\E\\QZX',
	    'PoRE[E R^IjI RRMZM R[PlP RVEVW RcCc[ R_E\\M R[VRW RcPaS[Z RcPfSlY',
	    'PoSE]E R`EkE RSI]I R`IkI R_NlN RXEXO ReEeN R_PaUdXj[ R_P]UZXS[ R]NRO',
	    'PoRF[F R^JjJ RSN[N R^OjO RWFWV R^D^Z R^DjDjO ReUk[ RjQgT RcOgX R\\URW ReY[Z',
	    'PoRF[F RSNZN R^QfQ R^UfU Rd[j[ RWFWW R^L^W R^LfLfU R`C]J R[VRW R^HlHlSj[',
	    'PoRE[E R^HkH RSMZM R\\NlN R]YkY RVEVV R]R][ RdGdY RkRk[ R`C\\L R[TRV',
	    'Po\\ElE RRE[E R_KiK RRMZM R_PiP RVEVW R_E_W RiEi[ R[URX RdV\\W RfV^W RiVbV RkUdV',
	    'PoRF[F RSNZN RaThT Rh[l[ RWFWV R]G][ RaLaV RaLhLhT R]GlGl[ RdCbG R[URW',
	    'PoREZE RRMYM Rf[k[ RVEVW RfCfZ RlXk[ RgOlT R`V[[ R\\G^L RjGhL RaQ[S RZVRW RaCaR`W',
	    'PoREZE R_LhL RSMYM R^ZiZ RVEVW R^Q^[ R^QiQi[ RdEkM RdCeG RbF[M RcCaH RZURW',
	    'PocEkE RRFZF RbLlL RSMZM RVFVW R_L_[ R`D[I R_L[P R`I^N RZURW RiLh[d[',
	    'PoRG[G R^HjH R\\NlN RSNZN R]TkT R[[l[ RWGWX RdCdN RdOd[ R\\WRX',
	    'PoRF[F R\\GlG RSNZN R\\NiN Rf[l[ RVFVV RgPg[ RlXl[ RgJkN R_W\\[ RdCcG_N R[TRW RaQ_X',
	    'PoRF[F R]IlI RSNZN R]QdQ RVFVV RhDkF RgT`[ RjMfV R[TRW RfCfLhY R]I]R\\Y RlXk[i[',
	    'PobEkE RREZE RSNZN RcOkO R`ZlZ RVEVW R\\F\\R RgEgZ R_W[[ R[VRX RaCaQ_W',
	    'PoREZE R^HgH RSMYM RdWfW RVEVU R^M^X RbHb[ R^MfMfW RlWl[ RZTRV R[DiDjVj[ R[D[RZX',
	    'PoRE[E R]GgG R\\MlM RSNYN Ra[k[ RVEVW RaPa[ RbCbM RkXk[ RjG`Q[S R[VRX RiS`U',
	    'PoRF[F RSNZN R^SlS R]YlY RWFWW ReCeN R]NlNl[ RkEiK R_F`K R\\VRX',
	    'PoRF[F RSNZN R[PlP RWFWV R\\E^J R^Uk[ RkEhL RbL]V RbDcJ R[URW RhPgUdX`YZ[',
	    'PoREYE R]KjK RRNYN R]RkR R[VlV RVEVW R\\E\\I RdMd[ RbH]Q RYVRX R\\EkElI',
	    'PoRFZF RSNYN Re[l[ RVFVW RjDfI R_UY[ RkJfO R^D_H RkPgTcV RZVRW R[H^U RZHcH`R^WbYdZi[',
	    'PoRFZF R\\LlL RSMYM RVFVV RiElH RgVb[ RjOfX RlWj[k[ RdD\\E RdQ\\S RZURW R`E`[\\[ RgCfOhZ',
	    'PoRF[F R^MjM RSN[N R^VkV R[[l[ RWFWW R^E^M R]QdQd[ R^EjEjM R[VRX RjO`Q',
	    'PoREZE R]MkM RSMYM RVEVW RdHd[ R\\HkHkM RhCgH R_C`G RdQ]XYY RZURX RkXgX R]M\\RlRkX',
	    'Po\\EcE RRFZF R[JlJ RSMYM RaQdQ RVFVV R\\N\\Z RaLaY RhDkG RlWk[ RZURV RfCfMhY ReXZZ',
	    'Po\\EkE RRF[F RSNZN R[OlO R]ZjZ RVFVV R]S][ R]IhIhO R]SjSj[ R[URW RbE`O',
	    'PoRFZF R[HlH RRMYM RVFVV RhCjF RgT`[ RjMfV RYURW R[NbNaV]V RlWk[i[ ReCeMgY R[H[RZY',
	    'PoREZE R[IlI RSMZM R\\NjN R_ZjZ RVEVU R_Q_[ R]EjEjN R^SjSj[ R_RZW RbG_S RZSRV RbE`P',
	    'PoRF[F R^HlH RSMZM R[QlQ R]XlX Re[h[ RVFVV RbMfO RbSeU R_C\\K R[URW R_KjKjUi[ R_K\\Y',
	    'PoRF[F R^JjJ RSMZM R^OjO R^UjU RVFVV R^E^U R^EjEjU RgWl[ R_XY[ R[URW',
	    'PoRFZF RbMlM RSMZM RbUkU Rb[l[ RVFVV RgEgU R]VaZ R^WZ[ R\\R_X RkDbF RZTRW R[E`E\\N`N^W',
	    'PoRE[E R^IjI RSL[L R^NjN R^RjR Rf[l[ RWEWU R^E^R RfRf[ R^EjEjR RlXl[ RaR_X]ZZ[ R[SRV',
	    'PoRE[E R^LjL RSMZM RdSkS Rd[l[ RVEVU R^E^L R^EjEjL R_VbZ R^S`X R^Q\\Y R[SRV RdLcZi[',
	    'PoRF[F R_JgJ RSNZN R]ZiZ RVFVV R]T][ R]TiTi[ RdCiIlJ R]MiMfS RcC\\JZJ R[URW',
	    'PoREZE R]HlH RSMZM RcSjS Rc[l[ RVEVU RdLdZ R^VbZ R]S`Y R]LjLhO R_C[K RZTRV R^P\\Y',
	    'PoRE[E R]HlH R^LjL RSMZM R^PjP R^ZkZ RVEVW R^S^[ R^SkSk[ RcCdG R[URX',
	    'PoRFZF R[HlH RSNZN R^[c[ RVFVV RcCc[ R]M_P RjLgP RgDkF R_UZW R[TRW RcKfSkY',
	    'PoRF\\F R_JkJ RSM[M R^RlR R^VlV Rh[l[ RWFWV R^N^[ R_D_J R_DkDkJ R^NlNl[ R\\URW',
	    'PoRF[F R^KjK RSNZN R^PjP RVFVW R^G^Z RdCdG R^GjGjP ReUj[ RcPfX RjTeV R[URX ReY[Z',
	    'PoRFZF R]JkJ RRNZN R]PkP R]UjU RZ[l[ RVFVW R]E]P RdEd[ R]EkEkP RZWRX',
	    'PoRFZF R\\IlI RSNZN Re[i[ RVFVW R\\EdEdO R_W[[ RcHhMlN RcH^N[O R`Q^X R[URX R]QfQeUkTi[ RiC`D',
	    'Po\\EbE RRE[E RSMZM R\\SlS R`[d[ RWEWW RdQd[ R^G`K RcGeK RjFhK R]MjMgOdP RZVRW RjC`E',
	    'PoRFZF R\\GkG R\\NkN RSNZN Rh[l[ RVFVV RcCcG RcPcZ RiPi[ RlXk[ RgJkN R]WZ[ RaG_M R[URW R^Q]X',
	    'PoREZE R[NdN R^[a[ RVEVV R]E]N R]EcEa[ RfEf[ R]UYY RaL]V RZTRW RfElEiMlSlWhW RSMXM^N',
	    'PoREZE R[LlL RSMYM RgYjY RVEVW R^P^Y RdNd[ R]RjQjX R`N[S RbI^Q RiD]J R]DkI RZVRW',
	    'PoREZE R\\HjH RSMYM RVEVW R\\D\\H RkDkH RaOdQ RfWeZ RdCcJfMlO R\\SiSgX RcI_NZP RZURW',
	    'PoREYE R\\LeL RSMYM Ri[l[ RVEVW RaEa[ RgFgU RlDl[ R`MeR RaK\\U RYVRX RdD\\E',
	    'PoRFXF RaIlI RSNXN R`PlP Rb[l[ RVFVW RiDiX RZM^M^W`Z R\\XY[ R[E]I RYVRX RdDdQcW',
	    'PoRFYF R_FlF R`MkM RRNXN R_SlS Ra[l[ RUFUW RfIfY R[E]H RZM^M]XZ[ R]Xc[ RdC`M RYVRX',
	    'PoREZE RRMZM RVEVW R^K^W RdCdK R^KjKjW R_E`I RjEhH ReWl[ RdNcV`Y[[ RZURX',
	    'PoRE[E RSM[M R^PjP R^UjU Rf[j[ RWEWV R^K^[ RdCdK R^KjKj[ RhElI R_F[J R[URV',
	    'PoREZE RSMYM R\\QkQ R]TjT R\\WlW RVEVW RdNd[ R`E[J R_GbJfKlM R`FjFgIbK[M RZURX',
	    'PoRF[F R^KiK RSMZM R[PlP Rf[l[ RVFVV R\\G\\J RcDdG RfPf[ R\\GlGlJ RlXk[ R_W[[ RaQ_X R[TRW',
	    'PoRE[E R]LjL RSMZM RdRjR Rc[l[ RVEVW R]G]J RdLdZ R]GkGkJ R^SaY RcCdG R_Q\\Z R[VRW',
	    'PoRFZF R^KjK RRMZM R^OjO R\\UlU RVFVV R^F^O RfQf[ R^FjFkO RdFdO`S RcCbF R[URW',
	    'PoRF[F RdFlF RbGiG R_GfG RSMZM ReRlR RbRiR R`SgS R]SdS RVFVV RlXl[ RaDcIfLjM ReOhP RaQdWfZj[ ReDhD RkKlM RiTfW^[ RiIcL]M R[URW',
	    'PoREZE R\\GlG RSMYM R\\TlT RVEVW RdQd[ R_LbO RgLkO RcCeG RhIeP R`I]O RZURW',
	    'PoaDgD RRE[E R^IlI RSMZM R^VkV R^ZkZ RWEWW R^E^O R^Q^[ R^QkQk[ RlMlP RgNkP R^EeDgN RdN]O R\\URX RjCcD',
	    'PoRF[F R\\HlH RSMZM R]RcR RVFVV R]L]R R]LcLcR RiCkF RhTa[ RkLgV RlWk[ R[URW RcU[W RfCfNhY',
	    'PoREZE R]JkJ RRMZM R]OlO R_ZkZ RVEVW R_S_[ ReJeS R_SkSk[ R]EkEkJ RZURX R]E]Q[Z',
	    'PoREZE RSMZM R[QkQ RVEVV R\\E\\I RcNc[ R\\EkEkH RfEfLkKkL RdRkY R^WZY RbS\\Y R_K\\M RaE`K RZURV',
	    'PoRF[F R\\GlG RSNZN RVFVV RdCd[ RcQlY RhMlP RcR[Y RiJfP R_J\\Q R_LcOaT R[TRW',
	    'PoRF[F R]HjH R]LjL RRNZN R[QlQ RVFVV R]D]L R]DjDjL RbNdP R_Q`TdXj[ RhRdW\\[ R[TRW',
	    'Po`HkH RSJ\\J R`MkM RSO\\O RfQkQ RTSkS RUWjW RR[l[ RSESO RSE\\E\\O R_S_[ R`DkDkQ RdXhZ R`D`K_P',
	    'PoRE[E R^IlI RSMYM R^NkN R^SkS R^YlY RVEVW R_G_[ RfHfY ReDfG R`C]K R[VRW',
	    'PoRG[G R]NjN RSNZN R[UlU RVGVW R]G]T RbCbT RfCfT R]GjGjT RgVk[ RaV\\[ RZVRW',
	    'PoREYE R^HbH RZMcM RRNYN R[[^[ RVEVV R^C^[ RfVb[ RbRcV RfQjZ RjOeY ReLiX RkJgV R[RYX RYURW ReHgV RcFkFhT',
	    'Po\\ElE RRF[F RSNZN RWFWW ReQeY RkJhM RfNjW RcE_J[K R_IbL_P[Q RaJdQ_WZX ReIhU RZURW RdEgS RcLdQeW Rc[_[',
	    'PoRE[E R]GkG RSMYM R\\NlN R^ZjZ RVEVW R^R^[ RdCdG R^RjRj[ R_H`M RhHfN R[VRW',
	    'PoRE[E RSMZM R[PlP R]UjU R_[c[ RVEVV RcLc[ R]LjLjV RhFkI RbGdJ R^E[K R[TRW RiC^E',
	    'PocFkF RRFZF RSNZN RcQkQ Rf[l[ RVFVV RdKdQ RcCcI RgSg[ R\\IlIkL RlXl[ R_Z][ RaT`Y RZURW R\\I\\S[Y RhM`N',
	    'PoREZE R]GkG RSMZM R^WeW Re[i[ RVEVW R^R^Y R^ReReW R\\OkOj[ RcIkM RcHaK\\M RdDcJ RZURX',
	    'PoSEkE RUH\\H RbLiL RROlO RTRjR RUViV RR[l[ R\\E\\H R_R_[ RbLbO RbHiHiK RdXhZ RUHUL\\L\\O RbEbH',
	    'PoRFZF R]IjI RSNZN R]NjN R]TcT Re[l[ RVFVV R]E]N R]O][ RfOf[ R]EjEjN RlXl[ RkReU RZTRW RcY[Z',
	    'PoRF[F R^IkI R^MkM RSN[N R]UlU R]ZlZ RWFWW R]Q][ R^E^M R^EkEkM R]QlQl[ R\\URW',
	    'PoREZE R\\GkG R_LiL RRMZM R_PiP R\\TlT RVEVW R_C_T RiCiT RgVl[ RaW\\[ RZVRW',
	    'PoREZE R\\FkF R\\JjJ RSLZL R[NlN R^RhR R\\VjV RVEVT RcPc[ RfNlT R_OZT ReJhQ RbI^R RYRRU RcD_O',
	    'PoRFZF RSMZM Re[l[ RVFVV R[G[K RdCdG ReLe[ R[GlGlK R`V[[ RlWl[ R\\QaS R^J[R RbP_X R^McM`V RZTRW RdLjLjTgT',
	    'PoREZE R^KgK RSMZM R\\NlN R`[c[ RVEVU RcNc[ ReTjY R]Q`T RjQgT RcOfV RaV[Y R_C^K R_FhFfN RZTRV',
	    'PoRFZF R^LiL RSNZN R\\QlQ R`[c[ RVFVV R\\G\\J RdCdG RdQd[ R\\GkGkJ RhTlY R^UZY RZURW',
	    'PoRF[F RSNZN R_PjP R_ZkZ RVFVW R]G]J R_K_[ RdCdG R_KjKjP R_TkTk[ R]FlFlJ R[URW',
	    'PoREZE RRMZM RVEVV RgLkN RjFgI RjRfU RcJiL RcRdVgYl[ R^F\\J R^Q\\V RbJ`M\\N RbV_YZ[ RZURW RcDbK RcObW',
	    'PoRE[E R`IiI R`MjM RSMZM RaWiW Rh[l[ RWEWV RaQaX ReFeO RaQiQiV R]ElEl[ R\\TRV R]E]R\\Y',
	    'PoREZE R[HaH RfHlH RSLZL RfNkN RfTlT RVEVT RfCf[ R_W[[ RZSRU RaC`TYV R[NaN_W',
	    'PoREYE RZJbJ RcJkJ RRMYM RVEVU R^C^[ RgCg[ R^MbP RhOlV R]NYW RfMbV RgIjU R^H[U RgHcT RYTRV',
	    'PoSE]E R`EkE R`HjH RTH]H R`LlL RYQeQ RXEXL ReEeL R_MjR RUUgUd[ R^MRS RcL]LRM',
	    'PoSE]E R`EkE RSI]I R`IkI RVN]N R_NlN RSNZN RVS_S Ra[k[ RVPV[ RXEXN RbPb[ ReEeN RlWk[ RjRaU R\\ZR[ R_YT[',
	    'PoSE]E R`ElE RSI]I R`IkI R_MlM RRMYM RUUiU RV[j[ RVOVZ RXEXM R_O_T RfEfM RUOiOiT RkXj[ R]LYMUM',
	    'PoRFZF R\\GkG R]LjL RSNYN R[PlP Rf[l[ RVFVV RfPfZ RlWk[ R_CaG RaT[[ RgCfG RcGcP_W RZURW RcJbS',
	    'PoRF[F RdFlF RSMZM R^NkN R^RkR R\\VlV RVFVV R^J^R RdCdJ RdRd[ R^JkJkR R[URW',
	    'PoSE]E R`EkE RSI\\I RaIkI R`NlN RRRlR R_O_[ ReEeN R`RdWlZ R^RYWRZ RXEYNRN R]MUN',
	    'PoRFZF RbHkH RSMYM R`OlO RbYkY R`ZiZ RVFVV R\\D`F RiTl[ RZK^M R_R[[ RfCfNcX RZURW',
	    'PoRFZF R^LhL RSNYN R\\RkR R[[l[ RVFVW RcLc[ RcDjKlK RcD\\K R^T_Y RiTfY RZVRX',
	    'PoREZE R]GlG RSMYM R^QjQ R`[d[ RVEVW R^J^Q RdQd[ R^JjJjQ RjVkY RhTkW R_U[Y RdCeG R[VRW',
	    'PoRE[E R]GhG R\\LlL RSMYM R`UjU R`ZjZ RVEVW R`P`[ RcCcL R^QjQj[ RjFbO[R R[VRW',
	    'PoREZE R]IjI RSMYM R]NjN RVEVW R]E]N RcEcN RgRg[ R]EjEjN RaN\\S R_W\\[ ReNiRlS R[URX R`R_X',
	    'PoRE[E R]IkI RSMZM R]PjP R]TjT R[WlW RVEVV R\\D\\G R]L]S RdFd[ R]LjLjS R\\DkDkG R[URV',
	    'PoRE[E RSMZM R]MlM RaWiW R^WfW Rh[l[ RWEWU R\\Q\\[ R]H]M R]EdEeV R]HlHlM R\\QlQl[ RhSjX RZTRV RkCcD',
	    'PoRE[E R`HiH R`KiK RRMZM R^SkS R^VkV R^ZkZ RWEWV R]D]M R^O^[ R^OkOk[ R]DlDlM RZURW',
	    'PoRF[F R^JlJ RSMZM R`QkQ R`UkU R`ZkZ RVFVV R`M`[ ReDeJ R`MkMk[ R^DlDlJ R[URW R^D^Q\\Y',
	    'PoRFZF R\\FkF R]JjJ RSMYM R[NkN R_VhV R_ZhZ RVFVT R_P_[ R^RiQh[ RhPlT ReJiR RbJ^QZTRU RcE_P',
	    'PoRF[F R_JiJ RSMZM R^SiS R^WiW R[[l[ RVEVV R\\F\\J R^N^W RdCdG R^NiNiW R\\FkFkJ R[URW',
	    'PoRF[F R^HjH R^LjL RSMZM R\\OlO RdTkT Rc[l[ RVFVV R^D^L RdOdZ R^DjDjL R^T`Y R^T[[ R[URW',
	    'PoRE[E R]IjI RSMZM R]MjM R]RkR R]VkV R[[l[ RVEVU R]D]M RdOd[ R]DjDjM R^O\\T RZTRV',
	    'PoRFZF R\\FkF R]IkI R[LlL RSMZM R[SlS R\\YlY Rd[h[ RVFVV RdCdL RbUdW RbPeR R[URW R^O\\Y R^OjOjUh[',
	    'PoRE[E RSMYM RbOeO R\\SlS RVEVW RdPd[ RfHfN RdRkZ R]IkIiM RdR]Z RaFhI R^DiEdG RdIaL\\N R[VRW',
	    'PoRE[E R]HjH R]KlK RSMYM R]VjV R]ZjZ RVEVW R]D]P R]R][ R]RjRj[ R]DjDjH ReMiP RdHfO RcO\\P R[VRW RlNlQiP',
	    'PoRFZF R\\HlH R^LdL RSMYM R^VcV RVFVV R^O^W R^OcOcV RhDkF RgUa[ RjMfW RZURW RlWk[i[ RfCfLfOhY R\\H\\RZY',
	    'PoRF[F R[JlJ RSNZN R^QiQ R^TjT R[XlX RVFVV R^L^Q R^T^X RfQf[ R^LiLiQ R^FiFiJ RbCaJ R[URW',
	    'Po\\ElE RRFZF RSNYN R\\TlT RVFVV R]I]Q RaIaP RfIfP RiXl[ ReWiX RcEaJ RbV`Y[[ RcQbX RZURW R]IkIkPhP',
	    'PoRF[F RSNZN ReNjN R[TlT RVFVW R]I]R R\\JjJjR R`EZK RdUgYl[ RcRaW_YZ[ R_FfFdJeO RaJ`O^O R[VRX',
	    'PoRFZF R]GbG RRMZM R]UjU R]ZjZ RVFVV R]C]M R]Q][ R]QjQj[ ReCeMlLlN RcNbQ RkFeI R[URW RcL[M',
	    'PoRE[E RSMZM R]NkN R]TlT Rc[g[ RVEVW R\\F\\I R^O]S RhNh[ R\\FkFkI RcCfF RaJ\\K RfIjJ RgReUZZ R[VRW',
	    'PoREYE RZHlH RSMYM R[PbP R[TbT R_[b[ RVEVW R[L[[ R[LbLb[ ReLeU R^C_F RhCfH RYVRX RjKj[f[',
	    'PoREZE R^IgI R[LlL RSMZM RVEVU RgTlY RiQgS RkOhR RdNhV RaN[Q RdSaV[Y Rd[`[ R_C^J R_FhFfL RaRZURV RbL`OcQeTeY',
	    'PoREZE R^HiH R^LiL RSLZL R[OlO Rf[i[ RVEVU R^D^L R^DjDjL RaT[Z ReV`[ RfRcY R_QZTRU R]SkRj[',
	    'PoRFZF R[FkF RSMZM R\\RjR RVFVV R\\J\\R RcCc[ R\\JjJkR RcQkY RcQ\\Y R`M`P RgLfP RZTRV',
	    'PoRE[E R]JjJ RSMZM R]OjO R`[d[ RVEVW R]F]O RdOd[ R]FjFjO ReTkY R`U[Z RjQfU RdOfV RcCcF R\\RaR_W R[URW',
	    'PoREYE R[KbK RdKkK RSMYM R[PaP R[UbU RVEVW R[E[[ R[EbEbK RdEkEkK RfVl[ RdPhX RcOkOhVeZa[ RYVRW',
	    'PoREYE R\\JaJ RiMlM RRMXM R\\PaP RVEVW R\\E\\[ RdDiDiL ReUj[ RcPgX RbPjPhVeYa[ RaD\\E RXVRW RaUZV RdFcM',
	    'PoRF[F R^KjK RSNZN R[OlO R_SiS RVFVW RhFkI R]WYZ RcFcI RaTeXl[ R]E[J R`Q\\Y RiTeX_[ R`L]W R[URW RiC]E',
	    'PoREYE R\\JbJ RSMYM R\\PbP RVEVV R\\D\\V RZDcDb[ RfDf[ RYURW RfElDiMlRlVhV RdUZV',
	    'PoRF[F RSMZM R]ZkZ RVFVV R]P][ R]PkPk[ ReUhX R\\H_L RbHfL RhHlM RkDgH R^E\\H ReDbG R`SeU RgQcV_X R[TRW',
	    'PoREYE RZIcI ReKlK RSMYM ReRlR R[XbX Rh[l[ RVEVW R[P[Z R^C^P R[PbPbX ReDlDl[ RYVRW ReDeRdY',
	    'PoRF[F R\\FlF RSMZM R[SlS RWFWV R^L^S R`C`I RgCgI R^LjLjS ReUj[ RdIcSeV RcU_Y[[ R[URW RcNbV',
	    'PoRFZF R^KhK RSMZM R_[b[ RVFVV R\\N\\[ R\\NbNb[ R]Q`R R^T`W RdTg[ RhTl[ R^EcDhIlJ RfNdT RkNhT RcD^IZK RZURW',
	    'PoRF[F R]JjJ RSNZN R]OjO R\\RkR R]WjW R[[l[ RVFVW R]F]O RcCcG RdRd[ R]FjFjO R[VRW',
	    'PoREYE R]IkI R[MlM RRMYM Ri[k[ RVEVW R]Q][ R]D]I RbQb[ RdCdI RfQf[ RkDkI R]QkQk[ RdMaS RZVRX',
	    'PoSE]E R`EkE RSI]I R`IkI R_NlN RZ[e[ RXEXN R[S[[ ReEeN RgTlY RfXe[ R[PaR RUSSX RfPbUZYR[ R[NRO R]MVN',
	    'PodElE RRFZF RSMZM R\\UkU R\\ZkZ RVFVV R\\Q\\[ R]E]M RdQdZ R\\QkQk[ RdNbN RkNgN RbD\\F RgEeM R`IcL[N RZURV RlFkN',
	    'PobElE RRFYF R[M`M RRNXN R[XaX RVFVW R[G[[ R]C\\G`G`M R[QaQaY RcJcW RgEg[ RYURW RcJkJkWiV',
	    'PoRFZF RSMZM R^ZiZ RVFVU R\\F\\J R_R_[ RcCcG R]TiTi[ R\\FkFkJ RdMkT RgJkN RcM[U RaJ\\N RZTRV',
	    'PoRF[F RSMZM R^NiN R^RiR R^ViV RVFVV R^J^V RcCcJ R^JjJjV RgElI R_E[I RfXkZ R`XZ[ R[TRW',
	    'PoRE[E RSMZM R_PjP R[TlT R^ZkZ RVEVV R^V^[ RdOdZ RkVk[ R_IdK RbHfI R`M^R RaFjEfJ\\N RcE[H R[URW',
	    'PoRFZF RRNZN R]OjO RY[l[ RVFVW R\\R\\[ R]E]O RaRa[ RfRf[ R]EjEjO R\\RjRk[ RdJgM RcGaL RZVRX',
	    'PoRE[E R]LiL RSMZM R]OlO Re[i[ RVEVU R_U`Y R]FiFiL ReUhW RbUdX RbCbG R\\UZZ R[TRV R]F]SkSj[',
	    'PoRFZF R\\FkF R]JjJ RSMYM R[MlM R\\UkU RVFVT RdRd[ RgMlR RaM[S RfJiP RdThXlZ RbH^Q RdT_X[Z RcC`N RYSRU RgP_Q',
	    'Po\\EkE RRFZF RdLkL RSMYM R[NlN R^VjV R^ZjZ RVFVV R^R^[ R^RjRj[ RiJkL R\\LbKbM R_G]L RgGeL RZTRW',
	    'PoVQiQ RVViV RS[l[ RSMSQ R_Q_[ RSMkMkQ RkEiG RUESG R^E[G RbE`G RXH]J RdXiY ReHlK RfCeHcJ_K RWIRK RYCXI',
	    'Po^ElE RRE[E R^IkI RSM[M R^MkM Rf[k[ RWEWU R`UaY ReEeQ RgTiV RdTeX R]U[Z R[TRV R^E^QlRk[',
	    'PoRF[F RSNZN R^TbT RfTkT R^ZkZ RVFVW R^O^[ RdGdK RfOkOk[ RiFlK R_E\\L RcM]O R[URW RiC_E',
	    'PoZFlF RUSjS RVWhW RR[l[ R_S_[ RbKbQ RhKhQlPlQ RcXgZ RaCcF RXLSQ R\\NYQ RTDXE R[JgHiI R`G[I RSHWI R\\K\\P',
	    'PoREZE R\\FlF RSMYM R]MlM R^QjQ R^ZjZ RVEVU R^T^[ RdCdG RdHdS R_JjJjQ R^TjTj[ RZTRV R\\F\\RZY',
	    'Po[EkE RRFZF RSMZM R\\NjN R[SlS RVFVU R\\I\\N RaEaN RcOc[ ReEeN R\\IjIjN RhXlY RcRjY RcR]XZZ RZTRV',
	    'PoREZE R\\KcK RSMYM R\\OcO RVEVV R\\F\\Y R_C_F R\\FcFcO RfEf[ RcRaTcY RZURW RbWZY RlWhW RfElEiNlTlW',
	    'PoRF[F R]KjK RSMZM R]PjP ReWkW RVFVV R]F]P RdFdZ R]FjFjP RjTkW R^W[[ RcCbF RhRfW R`Q^X R[TRW Rd[lZl[',
	    'PoRF[F R^HkH RSMZM R`NiN R`QiQ R`TiT R\\VlV RVFVV R\\L\\V R^C^H R`K`T R`KiKiT RfXl[ RaX[[ R[URW RiD^F',
	    'PoRF[F RSMZM R]NiN R]RiR R^ZjZ RVFVV R]K^U^[ R]KiKiR R^UjUj[ RdChH R_HgHlI RcC^HZI R[TRW R]N\\X',
	    'PoRFZF R\\GkG R]KkK RSNZN R[PlP RaTjT R^ZlZ RVFVW RdGdO ReTeZ R_D`F RhCgG R_R[Z R_P]X R[URX',
	    'PoRF_F RSI^I RiLlL RTO]O RSQ^Q RTV]V RXCXI RYQYZ RSLSM^L^O RcDcK RcDiDiL RbPdUhYlZ RaPjPhUdY`[ R^YYZTZ',
	    'PoRFZF RSMZM R]UjU R]ZkZ R[ZfZ RVFVV R]P]U RdMdZ R]PjPjU RhWl[ R^H_K RbFeG R_FcKjN R]EjEeK]N R[URW',
	    'PoRF[F R\\MkM RSNZN R[RlR R]ZjZ RVFVW R]T][ RdMdZ RcGcK RjTj[ R^G_K RjFgL R[URX RjC_E',
	    'PoRFZF RSNYN R\\OkO R_RiR RVFVV R\\EdEcI R\\K\\LkKkM R^F^I R_SdXk[ R_Q[Z RiFgJ RiSdX][ R`M]X RZURW RjCcD',
	    'PoREZE R_KeK RSNYN R_OjO R[UlU RVEVW R_K_U RfOfU R\\FkGkJ RcCdF RfXkZ R`XZ[ RYVRX R\\G[J RhJaJ',
	    'PoREZE R[FlF RSMYM R^OiO R[SkS R_[c[ RVEVW R`C`H RcOc[ RgCgH RgVkZ R_V[Z RcIfLlN RcI`L[N RZURW',
	    'PoRF[F RSMZM R_PiP RVFVV R\\H\\K R]S]Y R_K_P RdCdH RdPd[ R_KiKiP R\\HlHlK R^D_G RiDhG R[URV R]SkSkYhY',
	    'PoREZE R[HlH RSMYM R]PjP R]TjT R]WjW RVEVW R]M]W R]MjMjW R^DkDkK RfXl[ RaY[[ R^KaK RjKfK RZURW R^D]K ReDdKgK RcK`K',
	    'Po[FlF RRFZF R\\KkK RRNYN R\\NkN R[UlU Rb[f[ RVFVW R\\H\\N RdCdQ RfSf[ R\\HkHkN R^W`Y R[RiQkR RZURW',
	    'PoRFZF RSNYN RfSkS RVFVV R^L^[ RcGeJ RhHkK RbTdY R^C\\I R_I[Q RiEfL RcDaL RZURW RfNfZk[ RbP`Y',
	    'PoRFZF R\\FlF R`IhI RRNYN R]PkP R]TkT R]WkW R[[l[ RVFVW R]L]P R`C`I RdId[ RiCiI R]LkLkP RZVRX',
	    'PoTD_D RWH]H RiIlI RUJ^J RTP_P RTRjR RUViV RR[l[ RTDTP R_R_[ RbDiDiI RYL]M RWFVI RYKVO RaJeNjQ RdXhZ R`JiKfN`Q RcDbI RZGYM',
	    'PoRF[F R]IkI RSNZN R]OkO R]RkR R]VkV R]ZlZ RVFVW R]E]I R^M^[ RdCdI ReMeZ RkEkI R^NZR R^K\\Q ReKdP R[URW',
	    'PoRF[F RSMZM R]QjQ RVFVU R]F]Q RaU`[gZg[ R]FjFjQ RjVkZ RcCcF RcH_KgN RaSeU R]V[Z RbIgJdN_P RZTRV',
	    'PoRFZF R\\GkG R_JhJ RSNZN R_NhN R[QlQ R_VhV R[[l[ RVFVV RcSc[ RhCgQkW R_C`RZVRW',
	    'PoRFZF R[FlF RSMZM R]OjO R^WhW Rg[k[ RVFVV R\\R\\[ R]H]O RdCcF RjHjO R\\RkRk[ RfUiX R`IhM RfHcL_M RbO`W RZURW',
	    'PoREZE R\\FkF R[KkK RSMYM R]QiQ R]TiT Rf[l[ RVEVW R]M]T RfTf[ R]MiMiT RlXl[ RcCcF R`G`J RfGfK R_YZ[ RaT_Y RZURW',
	    'PoRFZF RSMZM R]NjN R]RjR RYUlU RVFVV R]J]Q RdJd[ R]JjJjQ R\\F_H RbFdH RhFjI RjChF R^C\\F RdCbF RdThYlZ RcT`XZZ RZTRW',
	    'PoRFZF RSMZM RVFVV R\\DbDbL RdDkDkL RiHfL R^E\\H R`I]L RgEdH RdNlR RaO[R RdQ]T RfS^W RZURV RiWdY\\[',
	    'PoRFYF RcHlH RYHaH RSNYN RgRlR RVFVV RcLlLkP RdWhZ RcSeY RdCbK RYURW R_[\\[ RhLgZl[ R]C]H\\QZZ R\\M`M`V_[ RdPbZ',
	    'PoRFYF R[GbG RZKcK RSMYM RZPcP RVFVV R_C_K_P RgVc[ RfQjZ RiPeY RZZX[ Ra[^[ ReMhW RfDcO RYTRV ReJgV ReJkIhV R]P[Y R\\TbTa[',
	    'PoRFZF R_FlF R`NkN RSNYN R`QkQ R_UlU Ra[l[ RVFVW R`J`Q ReCeY R`JkJkQ R[K^M R[D^G RZQ^Q]XZ[ RZVRW R_Zf[',
	    'Po[FlF RRFZF R_JhJ RRNYN Rh[k[ RVFVV R\\M\\[ R_C_J RcJc[ RhChJ R\\MkMk[ R`PaW RfPhPiV RhQeW R`R^W RZURW R`P^P RhPfP',
	    'Po]EkE RRF[F R[JlJ RSMZM R^PjP R^SjS R\\VlV RVFVV R^L^S RdSd[ R^LjLjS R`EaJ RhEfJ R[URV',
	    'PoREZE R^KjK RSNYN R]RkR R]VkV RVEVW R[G[J R]N]V RdKdV R]NkNkV R[FlGlJ RcCdF RfXl[ RaX[[ RZVRX',
	    'PoREYE R[FlF RSMXM RZNlN R[SkS R[WlW RVEVW R^J^S R`C`H RcJc[ RgCgH R[JjJiS RYVRX',
	    'PoREYE RSMYM RZTlT RVEVW R[D[M R[DbDbM RdDdM RdDkDkM R_NbO RhNkQ RaQ_V R\\PZQ RePcP R^Wk[ RZVRX RgUdY[[ R^G]O RgGfO',
	    'PoRFZF R^IgI RZLlL RSNYN R`PeP R[R^R R_TfT Rh[l[ RVFVV R[M[Y RhMh[ RlXl[ RcOcVfY RkPhT R`M`Q R_C^J R_FhFgL RZURW R_YYY RbRbY',
	    'PoRFYF RRMXM R^PaP RdZkZ RVFVW R[P[Y R[E[L_L^X R[EaEaL RdSd[ RdSkSk[ RgNlR RhMbS ReGhO ReGkGgO RfDbL RXVRW RaXYY',
	    'PoRFYF RSMXM RZMlM R[TkT RVFVV R]F]T RaGaT ReGeT R\\HjGiT R`W`[ ReWf[ RiVl[ R^C[I R\\WZ[ RYURW',
	    'PoRFZF R`LgL RSNZN R^UiU R[[l[ RVFVV R^O^U R^OiOiU RgIlN RiDfG RkHhJ R\\H_J[N R`V`Z ReDhL R\\EbE^L RgVfZ RZURW',
	    'PoRFZF R[FlF R[MkM RRMYM Rf[j[ RVFVU R`C`G R\\JcJdR RgCgG R`W][ RcLfOlR R]SgSfWkWj[ RaS_Y RcL`P[R RZTRV RiH`I',
	    'PoREZE RSMYM R\\OlO R^VkV R^ZkZ RVEVW R\\G\\O R^R^[ RdGdO R^RkRk[ R\\GlGlO R`IaM R_CaE RiIhM RiCfG RZURX',
	    'PoRFZF R\\HlH R]KjK RSMYM R[NcN ReRkR R[UlU Rc[h[ RVFVU R_N_R ReMeQ RhSh[ R]DjDjK ReMkMkQ R_VaY RcQ[R RZTRV',
	    'PobDkD RREYE RbKjK RSMYM RbOlO R[SlS RVEVW RbDbO RcQc[ RbHjHjL R[H^I R]D_F R_L\\P RcSgWlY RcS_W[Y RYVRW',
	    'Po\\GiG R\\IiI RZKlK ReSiS RTTjT RVXiX RR[l[ R\\D\\I R_T_[ R\\DiDiI RaN\\R RfNaS R^KYP RTDXE RRHVH RcYgZ RUNTR RWLUP R\\NjNiR',
	    'PoRFZF R[HlH RSMZM RdRlR RbWlW RVFVV RdCdN RdRdW RiNi[ R^D`G RjDgG R`V[[ RdGgKlM R\\T`V R]QbQ_X RdG_L[M R^N[URV',
	    'PoRFZF R[GkG RSMYM R^PiP R^TiT R`[c[ RVFVU R^L^T RcTc[ R\\MjMiT RgVkZ R\\I_K ReHkN RjIhK R_V[Z RaH[N RcC_K RZTRV',
	    'PoRFZF RSMZM RaNdN RfSlS RaYhY Ri[l[ RVFVU R\\P\\[ RaUaZ RdIdN RfPfS RaUhUhY R\\PlPl[ R\\IlIiL R^DjDdG RcJ\\M RbPaS^T RaFiI RZURV',
	    'PoRFZF R[HlH R\\LjL RSMYM R\\Q`Q RfQkQ R\\TkT RVFVV R`N`Q R\\N\\X`W`[ RcCc[ RfVf[ R\\EjEjL RkNk[ RfNfQ RZURW R\\Q[[ RjVfV',
	    'PoRF[F R\\FlF R`JgJ R[LlL RSMZM R]SjS R]WjW RVFVV R]O]W R`C`J RdLdW RgCgJ R]OjOjW RgXl[ R`XZ[ R[URW',
	    'PoRE[E R\\IiI RZLlL RSMYM R\\QjQ R\\TjT R\\WjW RVEVV R\\N\\W RcCcL R\\EiEiI R\\NjNjW RfXl[ R_XY[ R\\E\\I RZURW',
	    'PoREYE R[IlI RRMYM R\\NkN R\\QjQ R[UlU RVEVW RaCaI RfCfI R]D^G RjDiG R_J`M RcNcUgYl[ RhJfN RcU`YZ[ RYVRW',
	    'PoRFZF R]HjH R]KjK R[NlN RSNYN R]UjU R`[c[ RVFVW R]D]K R]Q]U RcKcN RcUc[ R]QjQjU R]DjDjK RhWkZ R^XZZ RZURW',
	    'PoRE[E R[KlK RSMZM R^ViV R^ZiZ RVEVV R^R^[ RdDdP RdRdZ R^RiRj[ R^G_I RdJjQ RcJ\\Q RiFgJ R[URV RiC_E',
	    'PoRFYF RaIkI RaMkM RSNYN RaQkQ RaVlV Ra[l[ RVFVW RaGaX RfGfV RZI]L R[D^G RYP]P]WY[ RcC^M RgDfI RYURW R_Zf[',
	    'Po[DlD RRFZF R\\HaH RSNZN R\\NaN RfNkN R\\UkU R\\ZkZ RVFVV R\\H\\M R\\Q\\[ RaDaQ RcQcZ RfDfQ R\\QkQk[ RfHkHkM RZTRV',
	    'PoRFZF RSMZM R\\QlQ RVFVV RfXi[ R[H^J ReHhJ ReNkMlN RkXk[ R[NaMbO RgOjP R\\U`V RjFgL R^D]H R`G]M RhDfH RiSeX_[ RZURV RdCcKeW R\\Q[Y',
	    'PoRFZF R\\FlF R]LjL RSNYN R]OjO R\\SkS RVFVW R]I]O RdCdS RaV`[hZh[ R]IjIkO RcUeW RjVkZ RhQlT R]W[[ R[URX',
	    'PoRFYF RZFdF RRNXN RVFVV R[O[W RaCaI RgVc[ RgRkZ RiQeY R]C]HYP RfMiX R[O_O_U[U Ra[][ RfJhV RfIkIhV RfCdN RYVRW R[LbLbUa[',
	    'Po\\EcE RRFZF RSMYM R`QgQ RVFVU R[J[M RaMaQ R[JlJlM RbKdM RhMkP RiFgJ R`R\\V R]M\\P R]F^I RcFbI RZTRV R^TaXeYl[ R^ThTeWbYZ[ RiDbD',
	    'Po_FkF RTH]H R_KlK R_NkN RUP]P RUSjS RVWiW RR[l[ RTDTMUP RTD]D]H RUK]K]P R_S_[ ReCeF ReKeR RbGbI RiGhJ RcXhZ RTISQ',
	    'Po`EdE RRFZF RSMZM R[TlT RVFVU R`C`H RcOc[ R]P`S RiJlM RiPgS R^H\\JaL RcSgXlZ ReIiJ R]HcHaK_N[O ReFkFjJgLcN RcS_XZZ RZURV',
	    'PocEjE RRFZF RcMkM RSNZN R^PkP RVFVV RcIcM RcCcH RgUkZ RjRgU RbReUeY RePhX R\\HkHkK RfP^S RcT]V RdW]Z RZTRW Re[b[ R\\H\\RZY RfK_K',
	    'PoRFZF R_HgH RSMYM R\\PbP RePkP R[SlS RVFVV R\\K\\P R_D_H R\\KbKbP RcQc[ ReKeP R_DgDhH ReKkKkP RcShXlY RcS^XZY RZURV',
	    'PoRF[F RSMZM R_PiP R]VkV R]ZkZ RVFVV R[H[K R]R][ R_K_O RdCdH RdRdZ R_KiKiO R]RkRk[ R[HlHlK R^D_G RiDhG R[TRW',
	    'PoREYE R[JbJ RSMXM R[NbN RZQcQ R^[a[ RVEVW R[F[N R]C\\FbFbN RgWc[ RfRjZ RiQeY ReMhX RjLgW RYVRW ReJgV ReJkIhV R^O^Q\\Y RfCdO R]UbUa[',
	    'PoRFXF R`IjI RRNXN R`PjP R`SjS R_VlV Ra[l[ RUFUX R`E`H R`L`S ReGeZ R`LjLjS R`EkEkH R\\M\\W_Z R[XX[ RZD[I RXVRX R\\MYM',
	    'PoREZE R[FlF RSNYN R`NgN R]PjP R^UiU R^YiY RZ[l[ RVEVV R]I]P R^R^X R`K`N RcCcF R`KfKfN R^RiRiX R]IjIjP RZURW',
	    'PoRF[F R_IgI RSMZM R\\QjQ R]WiW R]ZiZ RVFVV R\\L\\Q R]T][ RcLcQ R]TiTi[ R\\LjLjQ R_M_P RdCgGlI RgMfP RbC^HZI R[TRW',
	    'PoREZE R\\IjI R[LlL RRMZM R]SjS RVEVU R\\D\\H R]N]S RaDaH RbTb[ RfDfH R]NjNjS R\\DjDjH RdSeVhYl[ RaUYY RkUeW R[TRV',
	    'Po[EbE RdEkE RRFZF R\\IbI ReIkI RSMZM RdNlN R`[h[ RVFVV R_E_N RaSa[ RhEhN RbOdR RhXh[ RiSlY R]T[Y RiPcWZ[ RbM[O RZURW',
	    'PoRFYF RaIiI RSNYN Ra[l[ RVFVW RgPgW RZJ\\L ReNkU R[D]G RdEhElI ReIaM RYP]Q]WY[ RcT_V RjLfPbU RaE_I RYURW ReNbP_R RfXbX',
	    'Po^FjF RRFZF R\\HlH RSMZM R^QjQ R^TjT R^WjW RVFVV R^N^W RdCdL R^NjNjW R_D\\G RgYl[ RcHlL ReHbJ\\L R`Y[[ R[URW',
	    'PoREYE R[FbF RdFlF RSNYN RcUkU Rb[l[ RVEVW R_C_N RdPd[ RhChN R^FbJ RhEjIkL R[PkPjS R_E\\K RhEeL R]S[Z RYVRX R`Zg[',
	    'PoRFZF RbGfG RSMYM R]O`O RYUlU RVFVV R]E]U RbNbS RbCbKeKeU RgEjEjU RhWl[ R`D\\F R^XY[ R]J`J\\J RgJjJgJ RgPjPgO R\\URW',
	    'PoRFZF R\\HlH R]KiK RSNZN R\\NlN R[[l[ RVFVW R]T][ RaTa[ RaPaS RcCcN RfTf[ R]EiEiK R]TjTj[ RePfS RhPkS R]P\\S R[VRW',
	    'PoZFdF RRFXF R[KcK RSNXN R[NcN RZScS R^[a[ RUFUW R[H[P R_C_O R[HcHcP R_Q_S RgWc[ RhSk[ RaDbD RiQfY R[ZY[ RgNjY R]S[Y RXVRW RfJiW RfIkIhV R]VbVa[ RgCeO',
	    'PoREZE R^HiH RSMYM R^RjR R^TjT R^WjW RVEVW R\\F\\H R^O^W RdHdM R[OjOjW R\\FlFlH RdCdF RfXl[ RaK\\M RbX\\[ RiK\\O RZVRW',
	    'Po[ElE RRFYF R\\HkH R]MiM RSMYM R\\OjO RZRlR RbUlU R[YaY Re[i[ RVFVV R[T[Z R[TaTaY RcCcH RcMcP RiSi[ R[KkKkM RdWfY RYTRV',
	    'Po[G`G RULYL ReLiL RUSiS RVWhW RR[l[ RSOSR R[E[O R_S_[ R[EcEcO ReEiEiO RUETOkOkR RcXgY RYDUE RUHYH ReHiHeH R[I`J`L[M',
	    'PoRFZF RSMYM R[OkO RZWlW RVFVV R[DbDbL RcUc[ RdDjDjL R^QaT RgQjU RhDeH R^Q[U RhIeL RgQdT R`D\\H R`I\\L R^O^R RYTRW',
	    'Po\\DcD RRFZF RfHlH R\\LbL RSNYN R\\PcP RZ[l[ RVFVW R\\D\\P R]S][ R`L`Q RaSa[ R\\HcHcL RfSf[ R]SjSj[ RgKjN R`D`H RgDdM RZVRX',
	    'PoSDkD RTTjT RVXiX RR[l[ RVHVR R_D_R R_T_[ RbOfP ReOaQ R\\NYQ RYO]Q RcYgZ RdFlI RYJ\\KWL RbJfKaL RYFRI RUHiHiRfR',
	    'PoRFYF R[FlF RSMYM R]WjW RVFVW RdJdO RcCcF RfJfO R^RjQj[ RfGcJ RiIlO R]J[O RiOeO RkIfI R`O^O RYVRW R^P^U]Y R[JaJ`O',
	    'PocEjE RRF[F R_JhJ RSMZM R^UiU R^WiW R^ZiZ RVFVV R\\H\\K R_R_[ RcCcG R]SiRi[ R\\HkHkK RgLjO R`L]O RdNhRlS RcN_R[S RZURW',
	    'Po\\DkD RRFZF R^JaJ RfJiJ R]MaM RfMjM RSMZM R[PlP RVFVV R[G[J R\\T\\[ RaTa[ RdDdN RfTf[ R[GlGlJ RcPaU R[URW R\\TkTk[h[',
	    'PoREYE R[FkF R]KkK R]MjM RSMXM R]OjO R]RkR R\\UjU RVEVW R^I^S R`C`G RdIdR RfCfH R^I[M R^VcYk[ RZURW RiVdY[[',
	    'PoRFYF R[FkF R]JjJ R[NlN RSNXN RbRiR R]ReR R[[l[ RVFVX R]U][ R_C_H RaUa[ RcHcM ReUe[ RfCfH R]UjUj[ RfOjS RaN\\S RYWRX',
	    'PoRFZF R]IjI R]MjM RSNZN R[UbU ReUlU R[ZbZ ReZlZ RVFVV R[P[[ R]D]M R_P_Z R[PbPc[ RdDdM RePe[ RhPhZ R]DjDjM RePlPl[ RZURW',
	    'Po\\ElE RREZE R^HkH RSMYM R]NkN R^SjS R^UjU R^WjW RVEVW R^P^W RdCdH R^PjPjW R]JkJkN RgXl[ RaY[Z R]J]N RaJaN RfJfN RZURW',
	    'PoREYE RaKeK RSMXM RaPeP RZSkS RVEVW RcQc[ RbCaFeFeP RcRhWlY RkHhN R]DZJ R^H[O RiDgJ RcR^WYY R^JZJ RkJfJ RYURW RaF`N_PZO RkMkPfO',
	    'PoRFZF ReGlG RRNYN R]QjQ R]TjT R]WjW RVFVW R]M]W R]GbGaL RiGiK R]MjMjW RgXl[ R`X[[ RZVRW RjDeEdK RbD]E\\L',
	    'Po[DkD RRFZF R]IiI R]LiL RSMYM R`SgS R_VhV RVFVV R[N[Q R]G]N R`P`S RcDbG R]GiGiN R[NlNlQ RhPkS R]Q[S R`U[W R`WdYk[ RZURW RgWbYZ[',
	    'PoRFZF R[HcH RRMYM RVFVV R`D`O R^T`V R_KcL RhSfV R`H]KZN ReOlR RcVkZ RfCdJ RiGfM RcN`Q[S RbW\\Z RjOhO RZURV RcQc[`[ RbD[E ReGlGjO',
	    'Po\\FbF RdFkF RRFZF R[IbI RdIlI RSMYM R]QiQ R]TiT R]WiW RVFVV R]N]W R^IaL R]NjNjW RhJlM RgHiL R_J[N RfYl[ ReLcM RZURW R`YZ[ R_E^L RgDgK',
	    'PoRF[F RSMZM R]OjO R]QjQ R]SjS RVFVV R]L]S R]LjLjS R[HkHkK R^EfEbJ_J RaS_VZX RbJhJ R_WdYk[ R[URW R_C[H\\K R_VhVcYZ[',
	    'Po^FlF RRFXF RbNhN RSNXN RcUgU R`[l[ RVFVX R`K`X RcQcV ReKdQgQhU RZO\\O\\W`Z RdDfF R\\WY[ R[E\\I RbGbJ RXWRX R`KkKjXgX RhGgK R\\OZN',
	    'Po[FlF RRFYF R\\MjM RSMYM R[O`O RfOkO R[R`R RfRkR RVFVV R\\H\\L R_U_Z RcCcF RcNcQ R\\HjHjL RcThYl[ RjTeV R`HaM ReHeM RbSZVRW RdY[[',
	    'PocFkF RdIjI RdLkL RSNWN RdRkR RdTkT RhXkX Re[l[ RRFVFUV RZDZ[ RdOdX RgIhL RdOkOkX R^MaMaX^[ R_E`I RaXg[ RfCcJ RXVRW RZE^E\\L^P^T[T',
	    'PoREYE RZGbG RSMXM RdNlN R[QbQ RdRlR RdVlV RVEVW R[K[Q R^D^[ R[KbKbQ ReJeV RdJlJlV R^SbV RiXl[ RfXc[ RfFkFiJ R^P[X RfCdJ RYVRW',
	    'PoRFYF ReFlF R[FcF RZLcL RSNYN ReNkN ReQkQ R\\RbR ReTjT R\\VbV ReWkW Re[l[ R_[b[ RVFVW R\\O\\[ R\\G\\K R^C^F R\\ObOb[ ReNe[ ReCeIkIkN RaG`L RZURW',
	    'PocEjE RRFZF RaJhJ R^KeK RcMkM RSMZM R_QjQ R_TjT R[ZlZ RVFVV R_O_T RcCcM RdOdT RfVfZ R_OjOjT R\\HkHkJ RbVbZ R^VjVjZ R^V^Z RZURW R\\H\\RZY',
	    'Po[ElE RRFYF ReKlK RRMYM RdUkU R[[l[ RVFVV R_T_Z RaGaQ RdRdZ RhGhQ R]KaKcN RhIjO RhIeO R`J^O RcG]G RYURW RkGdG R[E[RYZ',
	    'PoREYE RdFkF R[FbF RRMYM RiNlN RVEVW R]P]V R_C_I RcIkIjN R]PjPjV ReXk[ R[DZG R\\I\\M ReDcG RgCgIdN RZIaIaM_M RYVRW RaYZ[ RcRbX',
	    'PoRFZF R[HbH ReHlH R[KbK ReKlK RSNYN R^NjN R_UiU Ri[l[ RVFVV R[D[[ R_Q_U R[DbDbK RdMdZ ReDeK R_QiQiU ReDlDl[ RdUhY RdU_Y RZURW',
	    'PoRFZF R\\GbG ReGjG R\\JbJ ReJjJ RSMZM RZTlT RVFVV R\\DbDbM ReDjDjM R\\D\\MaMbO ReDeMjMlP RfNdP R]N[Q RaQ^Wk[ RZURW RhUdY[[',
	    'Po\\DkD RSIZI RTQYQ RTTjT RUWiW RR[l[ RSDZDZH R]H]R R_T_[ RcDcQ RdYgZ ReKiKeM RaJ^M R^KbL RhNePiP RaN^PbP RgFlH R`F[HjIjRhR RSHRMZMYQ',
	    'Po[ElE RRFZF ReLjL RSMYM R[NkN R\\QkQ R[TlT RVFVV R\\LaL`T RcCcF RgLgT ReHjHjL RdVhYl[ RdTZX RkVfX R_V_Z R\\H\\L R\\HbHbL ReHeL RYURW ReY\\[',
	    'PoREYE R]GkG R^LkL RSMYM R^OkO R^QlQ RaZgZ Rh[l[ RVEVW R\\S\\[ R]D]G R^J^R RaWa[ RdCdG ReIeQ RaWgWgZ RkDkG R\\SlSl[ RbU^V R_H[L RfTjV RYVRX',
	    'PoRFZF R[FlF RSMYM R]MbM ReMkM R^RlR R^TjT R^WjW R^ZlZ RVFVU R]I]M R_P_[ R`C`G R]IbIbM RePeZ ReIeM RgCgG ReIkIkM R_PZTRV',
	    'Po[EkE RRFYF R\\GjG R]KhK R\\MjM RRMYNlO RcRlR R[UaU RVFVV R[Q[U R`X`[ R[QaQaU RcCcG RcKcM RdSfU RiWlZ R\\XY[ ReWe[ R[IkIkK RYURV RiPiVfV',
	    'PoRFZF R\\FbF ReFkF RdIlI RiMlM RSMZM R^QjQ R^TjT R^WjW RVFVV R^O^W R^OjOjW RfXl[ R]D\\G R_C_I\\N ReDeG RhCgIeM R\\IaIbL_L RaX[[ RiIiM RZURW',
	    'Po_EdE RRFYF ReLlL R_LdL RSNXN R\\NdN R]ScS RbVeW RVFVV R[H\\V\\[ R]P]S R^XaX`[ RaTbV R]PcPcS R[HeHeJ R_T^W R_I_L RjEkI RYVRW R_E_J RhChPjY R[QYZ RhMgZ RbJ\\J R\\TdTd[b[',
	    'Po^XfX R[XbX RXY_Y R^G^X RcSf[ RgDgJiW RVFVPTX R\\FVF RhD[F',
	    'PoeWiW RcMfP RcC`L R_ScXfYl[ R]D]L^S RbHkHkQjW RTETSS[ RYDYSWY R\\T^YVY R^CTE',
	    'PoaHkH R`MlM RYGYX RgRlV R]T`Z RfCfNiT ReQ`TdXj[ RbDaK RfKgR R^D^L_T R_DTETRSZ RfIeQ R_XUY',
	    'PoaNfN RfQiQ R`VkV RYGYX RaJgJgN RaGjGjP RkSkV R\\T_Z RbJaWi[ RbC_K R_TcYl[ R]D]L^S R^DTETSS[ R]XUY',
	    'PoSG_G RUM\\M RSQ^Q RW[[[ ReFeY R[I_M RVIRM R[G]K RXCTL RVQUU\\T[[ RhUhZaY RjDjMjPl[ RjDaEaQ_Z',
	    'PoSG^G RRK_K RRQ_Q RSU^U RYCYK RYQY[ RUMVP R\\MZQ RfTiYbY RiDiLiNk[ RaFaQ_[ ReEeRcZ RkDaE',
	    'PoSE_E RSM_M RTP]P RRS_S RV[Y[ RSHSM RWEWM RYSY[ R[E[M RSH_H_M ReFeY R\\V^Y RTVRZ RhUhZaY RjDjMjPl[ RjDaEaR_Y',
	    'PoRGZG RdGlG RRNZN RdNlN RdTlT RTITM RVCVG R_F_X ReIeM RhNh[ RhChG RjIjM RXIWM RaTaZ RYSRS RcDcNcQd[ RVNVTUY RcD[E[QYZ RaW[X',
	    'PoRF_F RZL_L RSN_N RSQ^Q RRT_T RVVVZ RSHXHXK RYCYF RZH[L[S RZH_H_K ReFeY R]U[W RSHSLWLWSTW RYV]X R[YSZ RhUiYbY RjDjLjPl[ RkDaEaR_[',
	    'Po`ElE RS[^[ Ra[l[ RXEXP RXRXZ RSS]S]W RfRfZ RaSkSkW RaSaWjXl[ RSSSW\\X^[ RiIlO RhE`H RfH`K R[M\\PWP RkHgIeL`O R\\G]M RgKgPcP RfHgN RUEUISP R]DTE',
	    'PoSEkE Re[k[ RYLfLf[ R\\PaU RlVk[ RYEYMXRWZ R_YT[',
	    'PoRE^E R_LlL R][l[ RVEVK]K][ ReDeW RlXl[ RXNZT RZYRZ RVHVMUQTUS[',
	    'Po]EkE RRG\\G RWGWT RlXl[ RcOeT R\\SRU RgY[[ RaE`LiKiVj[ R`J`P`S_X',
	    'PoRD^D R_MlM R][l[ RVDVK]K][ RfEfX RlXl[ RXNZT RZYRZ RVHVMUPTTS[ RjD_F',
	    'PoSD^D R`EkE R`KjK R\\[k[ RVDVK\\K\\[ ReRjX RhRbX RlXk[ RWOYT RbKfT RjLfT RZYRZ RVGVLUOUST[ R`E`O_W',
	    'Po\\EdE R`NeN RSPkP RW[b[ RR[^[ Re[l[ RYTeTf[ RlXl[ R\\VaX ReFlI RYEWHRI RWIfIdM R^IZLTN RYPW[',
	    'Po^EkE RRH^H RW[[[ Ri[l[ RVM\\M\\Y RbEaLiKi[ RlWl[ RWCXF RdOeU ReZ^Z RVHVQUX RaK`R RaHaN`S_[',
	    'Po_ElE Ri[l[ RSHSM RXCYNZY RSH^H^M RaS`Z RaPaW RbJbS RbFbN RbKiKi[ RlWl[ RdOeU RWRS[ R^WYY RXMUX RgY_Z RXIVU',
	    'PoVLeL RSPkP RT[a[ Re[l[ RXTeTf[ RlXl[ R\\G[JZL RbJgL R[VaX R\\EdDgHlI RYDVHRI RYPW[',
	    'PoRE\\E R^EkE RRX]X RRERZ R`R`Z RaMaU RaJaT RaGaQ RaEaO RUK[Q RcOeT RlWl[ RZIUT RaKiKiUj[ RgY]Z',
	    'PoSD^D ReXhX R\\[k[ RVDVK\\K][ RaMiMiV RkXk[ RWOYT RcEhEkL RaG^M RdMaV RZYRZ RVGVLUPTTTY',
	    'PoRD^D RcVjV R][l[ RVDVK]K][ RdFdV RjSjV RlXl[ RXNZT RhD^G RZYRZ RVHVMUPTTS[ RjJ_K RiO^Q',
	    'PoSD^D R_PiP RdWjW R\\[k[ RVDVK\\K\\[ R_J_R RdDdW RiJiR RjTiW RlXk[ RWOYT RZYRZ RVGVLUPTTT[ RiF]H',
	    'Po^EkE RRI]I RTX\\X Rh[l[ RTPT[ RXDXP RTP\\P\\X RaKhKi[ RlVl[ RcOeT RdY^Z RbEaM`P`S_[',
	    'Po_ElE RUK]K RZW^W RXPX[ RSP^P^W RbSbZ RbObV RbJbR RcEcP RbKjKkYl[ RYC_J RWGRK ReOgT RXCVH RhY^Z',
	    'Po^EkE RVM]M RRMZM RSZ\\Z Rh[l[ RSQS[ RSQ\\Q\\[ RaKhKi[ RlWl[ RZH]N RWDSM RcOeU ReY^Z RbEaKaM`P`S_Z',
	    'Po_EkE RSI^I RRP_P R[I[[ RaSaZ RaNaW RbIbR RTCVG RbEcOfT RlWl[ R\\C[G RbKiKiUj[ RVIVRTY RhY^Z',
	    'PoSFYF RSPkP RV[b[ RR[_[ Re[l[ RYTeTf[ R]GjFiI RlXl[ R_D[I RcFdJfLkN R\\VaX RcFbJ`L[N R[KRL RYPW[',
	    'PoRE]E R_FlF R`PjP R`VjV R\\[k[ RVEVL]K][ R`J`V RdFdK R`JjJjV RlXk[ RVGVOYS RZYRZ RUJUPUSUUTZ',
	    'Po_EkE RSI[I RRQ\\Q RRU\\U Ri[l[ RX[\\[ RRMR[ RSESI RSE[E[I RRM\\M\\[ RbEbLiKi[ RlWl[ RcOfU RfY_Z RbHaOaT`Z',
	    'PoTE]E R_ElE RTI\\I RTM\\M RRQ_Q RbEbLjKkYl[ RdOgT RTEUPSY R[S]XRY RhY^Z RbJbNbR`Z',
	    'Po_ElE RSM_M R\\Y^Y RUPUY RZNZ[ RTQ^Q^X RbRbZ RbObW RbKbT RbIbR RUORS RcEcOfT RTE^I RlWl[ RXJTR R]CYGSJ RbKiKiVj[ RgY_Z',
	    'PoSD^D R`IkI R`NkN R`RkR R_WlW R`E`N ReEeW R`EkEkN RVDVK\\K\\[kZl[ RWOYT RZYRZ RVGVLUOUST[',
	    'PoWNfN RRPkP RZ[_[ RU[[[ Re[l[ RRGRK RWJWN R_C_G RWTfTf[ RWJfJfN RRGkGkK RlWl[ RUDXF RgDeF R[U`W RWPU[ RbZ]Z',
	    'Po_ElE RRI^I Ri[l[ RYEYN RbKiKi[ RlXl[ RZJ]N RXHSN RVI[J RdOfU RTV]Z RWOTV RRR\\RZVXYS[ RgY_[ RbFbLaOaT`[ R\\DRE',
	    'Po_ElE RSG^G RRN_N RTZ\\Z RTRT[ RXCXG RTR]R][ RbRbZ RbPbW RbLbT RbJbR RcEcO RbKjKkYl[ RUIUM RdOfT R\\HZM RhY^Z',
	    'Po`ElE RRN_N RST_T Ri[l[ RUIUT RYEYY RSI^I]T RbRb[ RbKbV RcHcR RcEcP RbKiKj[ RlWl[ ReOfT ReZ`Z R]DSF R]XSZ',
	    'Po_EkE RYI_I RYQ_Q R\\G\\[ RbRbZ RbObW RbLbT RbJbS RbHbQ RbEcOfT RlWl[ R]C\\J RXORQ RbKiKiVj[ RVCVJ RSIWIVSUY RhY^Z',
	    'Po`ElE RTK^K RUT_T Ri[l[ RTFURU[ RYOYZ R\\O\\Z RTF^F^K RUO_O_[ RbKiKi[ RYCZF RdOgU RlWl[ RfY`Z RSQRZ RcEcLbPbTa[',
	    'PoRH]H RTQkQ RW[c[ RS[_[ Rf[l[ RYDYO RYUfTf[ RlXl[ RYHRO RkFhJ ReJiNlO RXJ\\L R\\VaX R`F_J ReFdKaN]O RYQW[ ReDeK R\\DRE',
	    'PoRE^E R_ElE RSP^P RST^T RSISO RWEWO RXQXY RZEZO RSI^I^O RaRaZ RbObW RbKbT RbIbR RbEbO RbKjKjVkYl[ RdOfT RgY^Z R^XRZ',
	    'PoSD]D R_EkE RbKjK RbOjO RaSkS R`WlW R[[k[ RVDVL\\K\\[ RbHbO RfHfW RbHjHjO RkXl[ RXOXU RYYRZ RUKUPUTT[ R_E_P]W',
	    'PoRFlF RSP^P R_PkP RSRkR RZCZH RdCdG RXVeUe[ RR[\\[e[lZk[ R\\N^P RTKXM RaKdN RhMkQ RdIbK RgJbP RXIUK R[JUP R\\WaX RXRW[',
	    'PoUPiP R[[a[ Rf[l[ RU[\\[ RSMSQ RXTfTf[ RSMkMkQ RlWl[ RXH\\J RkEhG RUETH R]E[G RaE`H R\\VaX ReHlK RdI_K RWIRK RYCXI RXQU[ ReCdI R^ZX[',
	    'Po`EkE RTI]I RTL]L RTP]P RRS_S Ri[l[ RTFTP RYPY[ RTF]F^P RcEbLiKi[ RlXl[ RZU^X RXS\\W RXCWF RYSRZ ReOfU RfY_Z RbHbMaR`Z',
	    'Po`ElE RRG_G RRN`N RSR^R Ri[l[ RWFW[ R[F[[ RSJ^J^R RbSa[ RbQbX RbMbU RbKiKi[ RlWl[ RZR_W RUURX ReOfU RWQTW RfZ`Z RcEcMbQ',
	    'PoaEkE RRHaH RTK_K RSQ_Q Ri[l[ RSNSQ RTETK RTE_E_K RSN_N_Q RbSa[ RcPcW RcJcS RcGcP RcEcN RcKiKi[ RlXl[ ReOfT RRT^T[XT[ RYCYPUW^Z ReZ`Z',
	    'Po`EkE RRF_F RUO\\O RVW[W R[Z^Z Ri[l[ RSKS[ RVRVX RYLYR RVR[R[W RSK^K^Z RbKiKi[ RlXl[ RUGVJ RXCYF R\\F[K ReOfU ReZ`Z RcEbKbMaR`[',
	    'PoRE]E R_EkE RVM\\M RTUXU RZU^U RRX^X RRERZ RTOTU RVHVM RTOXOXU RZOZU RVH\\H\\M RZO^O^U RaSaZ RaNaW RbFbP RlWl[ RcOeT RaKiKiUj[ RgY]Z',
	    'PoREkE RULiL RURhR RT[a[ RRORR RUHUL RZCZG R[H[L RdCdG RUHiHiL RROkOkR RlXl[ Rf[k[ RYUeUfZ RbHbL R\\WaW RYRW[ RcZ_Z',
	    'Po`EkE RRE_E RSK^K RRQ^Q RRT_T RX[\\[ RYCYQ R\\R\\[ RSH^H^N RaSaZ RbObW RbKbT RbGbP RSHSN]O_R RUVWX RbEcOfT RlWl[ RbKiKiUj[ RhY^Z',
	    'PoRE`E RSG_G RTL_L RYO`O RWP^P RRPZP RSRkR RU[][ Re[l[ RTJTL RYCYG RTJ_J_L RXVeUf[ RlXl[ R\\MZO R\\WaY RYRU[ RjDbF RjHbJ RlLgNaP',
	    'PobElE RSH`H RZQaQ RZWaW RZCZN RZSZW R^N^[ RdSdZ RdPdX RdLdU ReHeP ReEeN RdKjKkYl[ RUDVG RUTWVR[ RVLRN RYHUM R_D\\G RXH`L RUORU RTQXQUY RgOgU RgZbZ',
	    'Po`EkE RTO_O RUU]U RUY]Y RTHTO RURU[ RYIYO RUR]R]Y RSJ_I_O RbSbZ RbObW RbKbT RbGbP RXE\\E`I RUFRJ RVKVM RcEcOfT RlWl[ R\\K[M RbKiKiVj[ RhY^Z',
	    'PoaElE RRM`M RSS`S RUEUS RXFXS R[F[S RUG_G_S RbRbZ RcMcV RcKcT RcEcP RcKjKkXl[ R[U[Y RXUXY R^U_X ReOgT RUCSJ RTUSZ RiTj[ RhY_Z',
	    'Po`EkE RTH\\H R_IlI R_LlL RVO]O RSRkR RS[a[ RTDULVO RTD\\D\\H RVK]K]O ReCeF ReIeP RlYl[ RhFgI RbGbI R\\WbW RYUeUfZk[ RYRX[ RcZ`Z RTGSO',
	    'PobElE RSIaI RUNaN RVQ_Q RVT_T RVZ_Z Ri[l[ RVVV[ RVV`V`[ RdEdLjKj[ R[L[N R\\J`K RXJVL RVE]EZH RlWl[ RfOfU RgYaZ RVESHTTR[ RdJdOcRb[',
	    'PoRFkF R_KjK R_NjN RRN[N R_PkP RSRkR RR[b[ Re[l[ R_C^F R`H`P ReGeP RXUeUf[ RlXl[ RTHWK RWGUI RZHUM RaG\\K R\\LYOUQ R\\WbX RXRW[',
	    'PoTG\\G RaGjG RTJ\\J RaJjJ RTL\\L RaLjL RSRkR RS[b[ Re[l[ RTDTL RTD\\D\\L RaDaL RYUfUf[ RaDjDjL RlXl[ RZN\\P RiNkP RUNSP RcN_P R\\WbX RYRW[',
	    'PoaElE RYE_E RYL_L RVN_N RWR^R R\\V`V RXX]X R][_[ RTGUUU[ RYHYL RYCYG RZXZ[ R[T\\V RWPWQ^P^R RUT_T_[ RcSb[ RcMcV RcJcR RcKjKkYl[ RZTWW RdEdKdPgT RTG`G`I RhYaZ RTQRZ R\\IVJ',
	    'PoRIlI RXQfQ RXYfY RXCX[ RfCf[',
	    'PoRHlH RRMbM RUS_S RUY_Y RUJU[ R_J_[ RgCiF RlWk[i[ RdCdJfW',
	    'PoSGkG RYJfJ RYNfN RRQlQ RVZjZ RVQV[ RYCYQ RfCfQ RbQbViViT R]Q[VXW',
	    'PoRI`I R`KlK RUQ]Q RUY]Y RaZkZ RUCU[ R]C][ RaRa[ RgEgR RaRkRk[ RkD`F',
	    'Po^IlI RRK]K RaQjQ RSY\\Y RaZjZ RSRS[ RXDXR RSR\\R\\Z RaCa[ RjCj[ R\\DRE',
	    'PoYF_F RaHlH RcOjO RcVjV R][k[ RYCYI RXS]S]Z RYJYP_O_P RcDcV RjDjV RlWkZ RTI_I^K RXSWZ RTITSR[ R\\LVM',
	    'PoXNfN RRRlR RWVgV RWZgZ RSGSK RWQW[ RXJXN RXJfJfN RgQg[ RSGkGkK RVDXF RhDeF R_C_G',
	    'PoVJkJ RURjR RR[l[ R_C_[ RXDTN',
	    'Po^JkJ RRNYN R]RjR RRWYW R[[l[ RRERY RREYEYW RdCd[ R_E\\O',
	    'PoTK^K R`KkK RTR]R R`RkR R^[l[ RYDYY ReDe[ RUFRO RaF_O R^XRZ',
	    'PoTFkF RULlL RZQjQ RZUiU RW[l[ R_C_F RaNaZ R[NYS RYGfK RfFaIXK RULUSSZ',
	    'PoTFkF RULlL RYQjQ RZUiU RW[l[ R_C_F RaNa[ RZGZK R[NXS ReGdK RULUSSZ',
	    'PoRE_E RaIkI RaOkO R`VlV RZPZY RfDfU RYERK RXLSQ RZQSW R_I[M]TaWdYk[ RbE`M RY[U[ RVHYMZU RZE[Q',
	    'PoTJ\\J R_IlI R_NlN RSO\\O Rf[k[ RXDXW R_D_N R_DlDlN RcV^[ RTERM R]VRW R]RlRk[ ReDeMcV',
	    'PoRE_E RbIkI RSM_M RbNkN RSQ_Q R_VlV R][l[ RSISQ RSI_I_Q RfDfV RWVR[ RbEaM RUTYYcZ RYEYPWW',
	    'PoTG^G RaGkG RTJ]J RaJjJ R_MlM RWThT RWWhW Rd[g[ RWPW[ RYCYM ReCeM RWPhPh[ RUDSI RaD_I R^MRN',
	    'PoVLlL RVSlS Rf[l[ R`E`[ RVElEl[ RVEVRUX',
	    'PoRKiK RRPiP RdViV R^[k[ RRERV R^E^[ RREiEiU RlXk[',
	    'PoVMlM RVTlT Rf[l[ R`H`[ RVHlHl[ R`C^H RVHVSTY',
	    'PoRGlG RUPjP RUUjU Rd[i[ RUKU[ R_C_[ RUKjKj[ RcDiE',
	    'PoRPlP RRUlU Re[l[ RRKR[ R_K_[ RRKlKl[ RSEjE^H RWGgK',
	    'PoSDlD RUQiQ RUViV Re[i[ RUMU[ R`F`K R_M_[ RUMiMi[ RaGlJ RbDYIRK',
	    'PoeMjM RUSkS RUVkV Rf[k[ RaOa[ RUOkOk[ R_F[IUL RfFaK]M RZCWFRI RWFlFkM RUOUUTZ',
	    'PoZMeM RUSiS RUWiW Rd[i[ RUOU[ R_C_F R_O_[ RUOiOi[ RgIkK RWITL RZIZM R]GbI RRElElI RRERI',
	    'PoROlO RRYlY RRER[ R_E_Y RRElEl[',
	    'PoRQlQ RRYlY RRIR[ R_C_Y RRIlIl[',
	    'PoRLlL RRRlR RRERT R_E_[ RRElElT',
	    'PoRMlM RRSlS RRGRU R_C_[ RRGlGlU',
	    'PoRQlQ RRYlY RRIR[ R_C_Q RRIlIl[',
	    'PoRNiN RRTiT R^[k[ RRGRV R^C^[ RRGiGiT RlVk[',
	    'PoRQlQ RRZlZ RRHR[ R_H_Z RRHlHl[ R^C\\H',
	    'PoVJkJ RVNkN Rc[j[ RVEVN RVEkEkN R^T[XWZR[ RRSlSj[ R`E`M_T',
	    'PoWQeQ RWWeW Rc[j[ RWLWY R^L^W RWLeLeW RXHlHlT RXHRN RZCWJ RlKlSj[',
	    'PoUJhJ RUMhM RRQlQ R_[e[ RUFUM R_C_M RUFhFiM RYQWVhUf[',
	    'Po_GlG RRM^M RRV^V Ra[g[ RRERX RXEXV RRE^E^V RgGg[',
	    'PoRElE RXPfP RXUfU RSYkY RSJS[ RXJXU R_E_U RXJfJfU RkJk[',
	    'PoUIiI RUNiN RRRlR RY[_[ RUDUN R_D_N R_R_[ RUDiDiN',
	    'PoRM[M RRU[U RRERW RVEVU RRE[E[U RfEfX RlDl[ R_D_R^X',
	    'PoSTlT RSZlZ RSNS[ R_N_Z RSNlNl[ RVJYM RRHVJ R[H_JbL RhJkM RUESH RXCTF R_E\\H RaC^F RjChFeHiJ',
	    'Po_IlI RRN\\N RRW\\W R_[l[ RRERY RWEWW RRE\\E\\W R_I_[ ReCeI',
	    'PoUIiI RUNiN RRQlQ RUDUN R_D_N ReQe[ RUDiDiN RXWVYR[ RYQXX',
	    'PoUJiJ RUOiO RRRlR RUFUO R_C_O ReRe[ RUFiFiO RYWVYR[ RYRXX',
	    'PoRNZN RRVZV RRFRX RVFVV RRFZF[V RhWl[ RdS]Z RfQiY RgKcU RaF]P RaC^N RaHhHeS',
	    'PoRM\\M R_PhP RRR\\R Re[j[ RRHRS RWCW[ RRH\\H\\R RbC^M R`IlIlTj[',
	    'PoTRkR RTZkZ RTKT[ R_K_Z RTKkKk[ RRDUH RkDjGgJ R]C_I',
	    'PoRL[L RRR[R Re[j[ RRGRS RWCW[ RRG[G[R RbO]U ReT^[ RiLdV RcL`R R]EjEeH^LlLlQjZ',
	    'PoRH_H RSR]R RSX]X ReYkY RaYgY R_ZeZ RSLSZ RXCXG RXLXX RSL]L]X RgPlY ReDaY',
	    'PoRM[M R_NhN R]SiS RRU[U Re[j[ RRFRX RWFWU RRF[F[U RaC]M R_HlHlSj[',
	    'Po^EkE RRMZM RRUZU RRERX RVEVU RREZEZU R`MdUhYl[ R^LkLgUcY_[ R^E^Q]Y',
	    'Po]IkI RRMZM R\\PlP RRUZU RRFRX RVFVU RRFZFZU RfVk[ RbU\\Z RcCcPhX RcP`W RcLbU',
	    'Po\\IlI RRMZM R[QlQ RRUZU RRFRX RVFVU RRFZFZU RhCh[ R_VZ[ R`C`Q_W',
	    'Po_JlJ RRM[M RRU[U RRFRX RWFWU RRF[F[U R`MdUhYl[ RaF\\O RiKeTaX[[ R`IbS RaC^M RiIfS',
	    'PoUIhI RUNhN RUDUM R_D_M RdRd[ RUDhDhM RaNdPlT R]NZPRT RZQYWU[',
	    'PoRM[M RRU[U RRFRX RVFVU RRF[F[U RgNg[ ReFlM R_VZ[ RbG[M RdCfH RcC`H R`O_W',
	    'Po\\KlK RRNZN RRVZV RRFRX RVFVV RRFZFZV RgVl[ RfEiH R`U[[ RcCdOhY RbO_X RcKfV RcK`V RcHeS RcIbT',
	    'PoVIhI RVMhM RRQlQ RVEVM RWQWZ R_E_M RVEhEhM R_Q`TdXk[ RiSbV R`YS[',
	    'PoRElE RVMhM RSUkU RSZkZ RSPS[ RVHVM R_P_Z RVHhHhM RSPkPk[',
	    'Po_OlO R_YlY R_E_[ ReEeY R_ElEl[ R\\HZM RTISN RXDXQTZ RXQ\\W',
	    'PoRG`G RSQ_Q RSW_W RSKSY RYCYG RYKYW RSK_K_W RhWl[ RbV][ RfQjY RdQ`Y ReCeMcU ReHgU',
	    'PoSG\\G R[O_O RVUiU RVZiZ RVQV[ R_Q_Z R_C_O RVQiQi[ R_DaHfMlO R[HWMRO RhGcI',
	    'Po]LkL RRMZM R\\RlR RRUZU RRFRX RVFVU RRFZFZU RdCd[ R]E`J RjEgJ',
	    'PoSIkI RSMkM RRUlU RRYlY RRQR[ RSESM R_E_M R_Q_Y RSEkEkM RRQlQl[',
	    'PoRNZN RRVZV RRFRX RVFVV RRFZFZV ReSgY_Y RhDgJjY R]E]P[Z RbEbRaZ RjD^E',
	    'PoRFlF RVNhN RTUjU RTZjZ RTQT[ R_C_F R_Q_Z RVIhIhN RTQjQj[ RVIVN',
	    'PoRMZM R^NjN R\\SlS RRUZU Rf[j[ RRFRX RVFVU RRFZFZU R^H^[ RdDdS R^HjHj[',
	    'PoVUjU RVZjZ RVQV[ R`Q`Z RVQjQj[ RZI^M R]LRN R^DUEUM RaElEkOgO ReEcK`O',
	    'PoTIiI RSLkL RWVgV RWZgZ RXPX[ R_Q_Z RVRhQg[ R\\DXH RbLfQlS RcFhH R]H\\LWQRS',
	    'PoRMZM RRVZV RRFRX RVFVU RRFZFZU RcClL RbE[L RcM]O RgOdR]U RjSgWbY[[',
	    'PoRFlF RUViV RUZiZ RURU[ R_C_F R_R_Z RURiRi[ R\\FXK RfLjO RWJ\\M RcHYO RUOhN',
	    'PoRH_H RSR]R RSX]X RSLSZ RXCXG RXLXX RSL^L^X RgPk[ RcC`O RcHiHeT_[',
	    'PoRFlF RSQ[Q RSX[X RSJSZ RWJWX RSJ[J[X R_C_[ ReVa[ RgRkZ RhOcY RdHaR RcKiKeV',
	    'PoUHiH RULiL RUEUK R_E_K RUEiEiK RbPjOjP R\\LYOTQ R\\PXTSV RbLbP RYS\\VaYl[ RZSfSbV]YR[',
	    'PoUJiJ RUNiN RRVlV RRZlZ RRQR[ RUFUN R_Q_Z RUFiFiN RRQlQl[ R_C\\F',
	    'Po\\HlH RRMZM RRUZU RRFRX RVFVU RRFZFZU ReUk[ RgL`S RcI^M R\\M_NbQ\\T RjPdW[[ RcDcI',
	    'PoUHiH RULiL RRPlP RTTjT RSWkW RUEUL RXLXT R_E_[ RfLfT RUEiEiL',
	    'PoYIeI RWPgP RUViV RUZiZ RURU[ RWLWP R_R_Z RWLgLgP RURiRi[ R`ElK R_C\\FRK',
	    'Po]GjG R[LlL RRMZM R\\QlQ RRUZU Rb[g[ RRFRX RVFVU RRFZFZU RcCcL RgLg[ R_TbW',
	    'Po`GiG RRM[M RRU[U R^ZjZ RRERX RVEVU RRE[E[U R^R^[ R^RjRj[ RbE\\K R`IfOlR RiHbO\\R',
	    'Po^HjH R]NlN RRN[N R^TkT RRW[W R\\[l[ RRFRY RVFVW RRF[F[W RdCdN RdPd[',
	    'PoUHiH RUKiK RWZgZ RUDUK RWTW[ R_D_K RWUgUg[ RUDiDiK RZMRQ RYNfN_RTU R\\KXN_RdSlU R`RRU',
	    'PoSHkH RVUhU RVZhZ RVPV[ R_C_N R_P_Z RVPhPh[ RVDYG RgDdG R`HbKkO R^H[LRO',
	    'Po[HlH RRMZM RRVZV RRFRX RVFVV RRFZFZV R_M_Z RgVl[ RaK[R RjMfQ RePiY RcG`M RdLgW ReX[Z RcEeT',
	    'PoSKlK RWVhV RWZhZ RWRW[ RUFZF]E`E_P R_R_Z RWRhRh[ RXGYJ RfFeJ RaKcNlR R]K[NRR RhD_E',
	    'PoRHlH RUKiK RTNjN RRQlQ RUViV RUYiY RR[l[ RUSUX R_S_X R_C_Q RUEiEiK RUSiSiX',
	    'PoSKkK R[P_P RVVhV RVZhZ RVRV[ R_R_Z R_H_P RVRhRh[ RdMjP R_CiH RXNSP R_CTI RYHcGlH',
	    'Po^LiL RRMZM R\\QkQ RRUZU R_[c[ RRFRX RVFVU RRFZFZU RcLc[ RcCkL R^UZY RbE[L RiUkY',
	    'Po_GkG RRNYN R\\QlQ RRWYW R^XlX Rc[h[ RRERX RVEVV RREYEZV RbSeV RcLeO R`C]K R`KjJjUi[ R_K^W',
	    'Po[JkJ RRMZM RRUZU RRFRX RVFVU RRFZFZU RfIfOkNkO RgFkJ RaP[V R_SdXl[ RaD`I_N[O R_RiRdW`YY[',
	    'PoUIiI RUMiM RTQjQ RRVlV RUDUM RZMZV R_D_M RdMdV RUDiDiM RcXl[ R[XR[',
	    'PoRElE RTH\\H RTM\\M RaMjM RUUiU RUZiZ RTHTM RUQU[ R\\E\\Q R_Q_Z RaEaQ RUQiQi[ RaHkHkM',
	    'PoYHeH RSKkK R[P_P RVVhV RVZhZ RVRV[ R_R_Z R_K_P RVRhRh[ RdMjO RXNSP R_CeFlH R_CXGRH',
	    'PoVHhH RVKhK RXTgT RXXgX RR[l[ RSNSQ RVEVK RXQX[ R_E_K RXQgQg[ RVEhEhK RSNkNkQ',
	    'Po\\GkG R\\JkJ RRMYM R[NlN R[RlR RRUYU Rd[h[ RRERX RUEUU RREYEYU RhNh[ R`UcX R_TZY RaN^V RcD`R',
	    'PoRHlH RUJhJ RUMiM RRPlP RXUfU RXXfX RUZjZ RUQU[ RXRXW R_R_W R_C_P RXRfRfW RVEhEhJ RjQj[',
	    'PoWPgP RTVjV RTZjZ RRHRL RTST[ R_C_H R_S_Z RTSjSj[ RRHlHlL RUDXG RhDeG RWKgKgP RWKWP',
	    'PoRNZN RRVZV RRERX RVEVV RREZEZV RfVk[ R]TbX R_IbL RhIkM RjUdZ RaI\\N ReRhX R\\HaJ RdEkEhJ RdQkQhX R\\EbE_K RdHiIfLbN R\\QbQ`V\\ZY[',
	    'Po]FkF RRNZN RRVZV R^WeW Rd[j[ RRERX RVEVV RREZEZV R^Q^X R^QeQeV R\\NkNj[ RcHkK RbJ]L RdCcI',
	    'PoRMZM RRUZU Re[l[ RRFRX RVFVU RRFZFZU R\\G\\J RcCcG RfLfZ R\\GkGlJ RlXl[ R]Q`T R`V[[ RbQ^Y R^J\\S R^McM`W ReLjLjUhU',
	    'PoSEkE RWJhJ RWMhM RSPkP RVUiU RVXiX RR[l[ RVRVX RWGWM R_G_M R_R_X RWGhGhM RVRiRiX',
	    'Po^IiI R[NlN RRNZN R^UiU RRVZV R[[l[ RRERY RVEVV RRFZEZV RdPd[ RgOlS RhDlH RaN[S R_D]H ReIiQ RcH_Q RcD`N',
	    'Po^HiH R^LiL RRMZM R[OlO RRVZV RRERX RVEVV RREZEZV R^D^L R^DiDiL R_Q[U RbT\\Y ReWa[ RgRcY R^SkRi[e[',
	    'Po\\HlH RRNZN R]PkP R]TkT RRVZV R]WkW R[[l[ RRFRY RVFVV RRFZFZV R]L]S R]EdEd[ R]LkLkS RjD`E',
	    'PoUHiH RULiL RRU]U RaUlU RRZ]Z RaZlZ RRPR[ RUDUL RWPWZ RRP]P][ R_D_L RaPa[ RfPfZ RUDiDiL RaPlPl[',
	    'PoaLkL RSM]M RROlO RTV^V RTY^Y RTRTZ RYRYY RTR^R^Y R[I]M RTGWJ ReMjO RbGeI RhIkL RiEbL R[FTL RdCcG RWCUG RgSbY][ R_C_LcVfZk[mW',
	    'Po^FlF R^MlM R`QjQ RST]T ReUkU RSZ]Z Re[l[ RSOS[ RXOXZ RSO]O][ RfCf[ R`IjIjQ R`VdZ RRGTN RVHXM RZH\\M RSCRI RWCVI R[CZI RaS_[',
	    'PoRMZM RRVZV RRERX RVEVV RREZEZV RdDkDkL RiHeL R_E\\H R`H\\M RgEdH RdMfPlR R[DbDbM`P[R RcR]T ReU]W RjWdY\\[',
	    'PoVGhG RVJhJ RWVgV RWXgX RR[l[ RSPSS RVEVJ RWSW[ R_E_J RWSgSg[ RVEhEhJ RSPkPkS RTK[L RjKcL RaNkO R\\NRO',
	    'Po]FkF R\\KlK RRNZN R^QjQ R^UjU RRWZW R]XkX R\\[l[ RRFRY RVFVW RRFZFZW R^N^U RdNd[ R^NjNjU RcCdF RhGfK R`G`K',
	    'Po[HkH RRNYN RdQlQ RRVYV RdWlW RRERX RUEUV RREYEYV RcCcN RdSdW RiOi[ R]D`G RiDfG R]U`V[[ RcHfKlM R^O\\T R]RbQ_X RcH`K[M',
	    'Po\\EkE R^JjJ R^MjM RRNZN R\\PlP R^UjU RRVZV R^XjX R[[l[ RRFRY RVFVV RRFZFZV R^R^X R^G^M RdGdM RdRdX R^GjGjM R^RjRjX',
	    'Po\\DlD RSJZJ R^JiJ R^MiM R\\OlO RSSXS R^UjU R^XjX R[[l[ RU[Y[ RUPUW RSDZDZJ R^R^X R^G^M RdGdM RdRdX R^GjGjM R^RjRjX RXVRW RTJSOZOZUY[',
	    'Po[EkE R\\HjH R]MhM RRMYM R\\PjP RZRlR RbUlU RRUYU R[Y`Y Re[i[ RRFRX RUFUU RRFYFYU R[T[Z R[T`T`Y RcCcH RcMcP RiSi[ RZJkJkM RdWfY',
	    'PoRE\\E RcElE RSM[M RdMkM RRU[U RcUlU RRZ[Z RcZlZ RRPR[ RSHSM RWPWZ RSH[H[M RRP[P[[ RcPc[ RdHdM RgPgZ RdHkHkM RcPlPl[ RaDaT][ R^I]P RaM`X',
	    'PoWGgG RWIgI RSN]N RaNkN RSP]P RaPkP RR[l[ RSKSP RSSSU RWDWI RXKXP R_D_I RSK]K^P_S RaKaP RfKfP RWDgDgI RaKkKkP RSSkSkU R\\WbY R[SXY RZUfUd[',
	    'PoWGhG RWIhI RSM]M RaMkM RSP]P RaPkP RWWfW RWYfY RR[l[ RSKSP RWDWI RWTW[ RXKXP RSK]K]P R_D_I RaKaP RfKfP RWTgTg[ RWDhDhI RTRSSjRjT RaKkKkP',
	    'Po_OiO R_ZlZ R`F`Z RSFjFgL RWMUX RWQYV\\YeZ',
	    'PoTFkF RRMlM RUQiQ R_UjU R^[l[ R`C`[ RUIiIiQ RWVR[ RXSUY RWV[Zd[',
	    'Po_IgI R]MlM RWTgT RWXgX RS[k[ RWPWX R_E_M RWPgPgX RWJRO RSEkEjI RXGUM RXI[LcM',
	    'Po]GlG RXO\\O R^QkQ RTLTX RXIXW R^K^Q RdCd[ R^KkKkQ RfTlZ RcR\\Y RRE\\EXJ ReRgV R\\VRX',
	    'PoSE\\FkG R]MjM RXO\\O Ri[l[ RTKTX RXIXW RcPcZ RdCdG RiPi[ RlXl[ RfJkN R\\EXJ RbG_M R\\Z[[ R\\URX R_Q^X',
	    'PoSEkE RVNhN RVQhQ R_WhW R][l[ RVKVQ R_C_H R_K_Q R`T`Z RVKhKhQ RSISJkIkK RRTkTkW RYVTZ R[Yd[',
	    'PoTK]K RTP]P ReSkS RRT]T RTCTK R]H\\K RaWdZ RXOXU\\Y RWWS[ RiGdI R_DkDgH RaHhJ RULRR R_MlMjQ R\\ESG RaP_Z RfMeZj[',
	    'PoXGlG RRIUL RXGWSRT R`C`G RWNVX',
	    'PoWGlG Re[j[ R`C`G RSIUM R\\WX[ RWRRT R[KfKeQlQj[ RWGWQVX R^K^P]V',
	    'PoWGlG RZMlM R^[c[ R`C`G RcMc[ RSIUM RWRRT RWGWRUY',
	    'PoWGlG R]ZjZ R]I]Z R`C`G RkVjZ RSIUM RWGWTS[ RWQRS RhLcO\\Q RWOVX',
	    'PoWGlG ReWjW R`L`[ RZLjLjV R`CcF RSIUM RWQRS RWGWQVX',
	    'PoWGlG R[LkL R^[b[ RaCbG RcQc[ RTIUM RWQRS RjMbP RWGWRVX',
	    'PoWFlF RZLjL RXZlZ RbLbZ RaCbF RSITM RWFWTS[ RWQRS RWNUX',
	    'PoWFlF R\\LkL R\\ZjZ RaCaG RjWjZ RaT\\Y R\\PgPcR RSIUM R]HZP RWQRS RWFWRVX',
	    'PoWGlG R`C`G RgVl[ R]MYQ RSIUM ReQhX RWGWTS[ R_H\\O RWQRS R^LfLcT^YY[ RWOVX',
	    'PoWFlF RZMlM Ra[f[ RfIf[ R`DcF RSITM R^Q_U RWQRS RWFWRVX',
	    'PoWGlG RYKlK RaCaG RaKa[ RSIUM RaOkS RWGWTS[ RWQRS RWOVX',
	    'PoWGlG R[YjY R[N[[ R`C`G RbJbY RjNj[ RSIUM RWGWTS[ RWQRS RWOVX',
	    'PoWFlF R\\KjK RZOlO RaZgZ R`CbF RSHTM RWQRS R_O]TiTgZ RWFWQUX',
	    'PoWGlG R]LjL RYRlR R]ZkZ R]L]Z RSIUM RaCbG RWQRS RWGWQVX',
	    'PoWGlG RZKkK R`QiQ Rc[h[ R^VY[ RSIUM RaCbG RWQRS RiRh[ RWGWQVX R`K_V',
	    'PoWGlG Re[h[ R_RZW RcV^[ RSITM RbCcG RfPbX R`P^T RWQRS R[JgJ[PkPi[ RWGWQVX',
	    'PoWGlG RaCaG R_UZZ RSIUM RbPeVhYl[ RiLgR R`Q]X RWQRS R\\LZR RaI_U RaIdU RWGWRVX',
	    'PoWFlF RYNlN RcZkZ RaCaG RcQcZ RlWlZ RSIUM RfIjK RWQRS R_U\\YX[ RWFWRVX RbJ`U',
	    'PoWGlG R[RiR R[[j[ R\\K\\Z R`C`G RbKbR R[KjKjS RkWkZ RSIUM RWQRS RWGWRVX',
	    'PoWGlG R`C`G RgPg[ R]WZ[ RSIUM RcIhNlP RWGWTS[ RWQRS RbI]NYP RWOVX R^P^T]X',
	    'PoWFlF RYRlR R[LiLiQ R`CbF RbHbQdUhYlZ RWQRS R`U]YYZ RSHTM RWFWQUX RbL`V',
	    'PoWFlF R[JcJ R[QkQ R[J[[ RaCbF RlXl[ RSITM RgYj[ RVQRS RaYZZ RcKeW RWFWQUX RiI`J',
	    'PoWGlG R[JkJ R[RlR Rb[f[ R\\M[Q RfJf[ RSITM RaCbG RWQRS RfQbUXZ RWGWQVX',
	    'PoWFlF RgRlR RYUlU RaCaF RbQb[ RZKgKgR RlOlR R`N\\R RWQRS RSITM RaH_O RWFWRUX',
	    'PoWGlG R\\ObO Rd[k[ R\\I\\[ ReIeZ RhOdQ RkLgP RlWlZ RSIUM R`CaG RWGWTS[ RWQRS RaYZZ RWOVX',
	    'PoWFlF RfOkO RaCaG R]IfIfO RkMkO R]MZP RSIUM RWFWTS[ RWQRS R]SaWdYk[ R[RhRdW`YY[ R]I]N RWNVX',
	    'PoWGlG R[JlJ R^PjP Rc[i[ RaCbF RaV]Z RSIUM R[J[TX[ RWQRS RjQiZ RWGWRVX R[OZX RcLbV',
	    'PoWFlF R]I]PaT R_DbF RcV_Z RSITM RfPjY RdQaY R\\RYY RWQRS ReMhV RWFWRUX R]K[V ReJdU',
	    'PoWFlF RaUfU R][i[ R]P]Z R`DcF RjWi[ RSITM RbIfMlO RVQSR RWFVTS[ RbH\\NXP R]PgPfU RVOUX',
	    'PoWFlF RlWl[ R]NfW R`DbF RbS\\Y RSHTM RdMaT RWPRR R[JiJiUjZ RWFWRUX R[J[SYY',
	    'PoWGlG RZLkL RZSjS RXZlZ RaCaG RbIbL RbNbZ RSIUM RWRRT RWGWQVX',
	    'PoWFlF R\\TeT R\\[k[ R]N]Z RaCaF R\\NeNeS RlWk[ RSITM R]HZP RWQRS RiVeV R\\KjKjQiV RWFWRUX',
	    'PoWFlF R[PiP RaCaF RcHcP RSHTM R^P_TcXk[ R[K[UX[ RVQRR RhRdX\\[ R[KkKjN RWFWRUX R[OZX',
	    'PoWGlG RYMlM R]ShS R]ZhZ R]I][ R`C`G RhIh[ RSIUM RWGWTS[ RWQRS RWOVX',
	    'PoWFlF RZKlK R\\VdV Re[i[ R\\O\\W RaCaG R\\OdOdV RiKi[ RSIUM RWQRS RWFWRVX',
	    'PoWFlF R^PbP Re[l[ R[M[Z R^I^Y RaCaF ReIe[ RlWl[ RSHTM RVQRR RjOeQ RcYXZ RWFWRUY',
	    'PoWFlF RXNlN RbUhU R\\ZkZ R\\I\\Z RbIbU RhIhU RaCbF RSITM RWFVTS[ RVQRS RWNUX',
	    'PoWFlF R[PlP RZZfZ R[J[W RaCaF RkVkZ RSITM RgXjZ RWQRS RWFWRUY RcVXW RcJeU RhH[J',
	    'PoWGlG R\\PhP R\\VhV RXZlZ R\\K\\V R`C`G R\\KhKhV RSIUM RWGWTS[ RWQRS RWNVX',
	    'PoWFlF RaCaF RSITM RcHgMlO RWFWTS[ RbH\\NXO RWQRS RbO[R RfQ\\V RiSgVaYZ[ RWOUX',
	    'PoWFlF R`[l[ RaCaF RZK^JbKbN RSHUM R[UYZ RWQRS RZOjOdU\\X R]Zf[ RWFWQVX RiI_J',
	    'PoWFlF R[RjR R[J[R R[JjJjR RfUl[ RaCbF R^UYZ RSITM RWFWTS[ RWQRS RWOVX',
	    'PoWFlF RaCaG R`HZN RSIUM R^KcPkT R`TeU RWFWTS[ RWQRS R^KhKcPZT RiX\\Z RWOVX',
	    'PoWGlG R\\PhP R\\UhU RX[l[ R\\K\\Z R`C`G R\\KhKhZ RSIUM RWGWTS[ RWQRS RWOVX',
	    'PoWFlF R\\LjL RYRlR RaCaG RSIUM RbLbReWgYl[ RWFWTS[ R]HZP RWQRS RaU^YX[ RWNVX RbOaV',
	    'PoWFlF RZPiP RaCaF RdHd[ RYKiKiO RSHTM RVQRS R\\ZZZ RYOYTkTjXfX RWFWRUX R_H_S^X',
	    'PoWGlG R\\UdU Rd[i[ R\\O\\W RaCaG R\\OdOdU RSIUM R]HYP RWQRS R\\LkKjTi[ RWGWQVX',
	    'PoWGlG RaMkM R[ZiZ R[R[[ RaCaG RaHaR R[RiRi[ RSIUM RWQRS RWGWRVX',
	    'PoWFlF ReYkY RaCaF ReKe[ ReKkKk[ RVQRR R`Z]Z RSITM RWFWRUY R\\H\\OZX RYLaLaU`Z',
	    'PoWFlF RYKlK RYZkZ RaCaG R^RaU R[P_R RfUl[ RbHaK_N]Q RfP\\Z RSIUM RWFWTS[ RWQRS RWOVX',
	    'PoWGlG R\\MlM RbPkP RbUkU RaCaG RbLb[ RSIUM R^HZP RWQRS RWGWQVX',
	    'PoWFlF RZKlK Rf[k[ R[O[[ RaCaG R[OkOk[ RcKcRhW RbS^W RSIUM RWQRS RWFWRVX',
	    'PoWFlF R[NjN R_[j[ R_P_Z RaCaF R[JjJjN RkWjZ RSHTM RVQRR RiR^U RWFWRUX R[J[SYZ',
	    'PoWFlF RZKkK RcRkR RXZlZ R\\P\\Z RaCaG RcKcZ RSIUM RWQRS RWFWRVX',
	    'PoWFlF R[OjO R[TjT Rf[j[ RcJc[ R[JjJj[ RSIUM RbCcF RWQRS RWFWQVX R[J[SZY',
	    'PoWFlF R[TjT RYZlZ RbTbZ R`CbF RVQRR RSHTM RbNlQ R[JhJeM`OYP RWFVQUX',
	    'PoWFlF R\\PiP R\\UiU RYZlZ RaCaF RbPbZ RSIUM R\\IcIgNlO RWQRS RbI]NYP RWFWRUX',
	    'PoWGlG R[SiS RcYjY R_YeY R\\YcY RXZ`Z R[L[S RbHbY R[LiLiS RgVkY R`DcF RSIUM RWQRS RWGWRVX',
	    'PoWFlF R]MhM R_VfV Rg[k[ RZJZ[ R_P_X RaCaF R_PfPfV RZJkJk[ RSIUM RWQRS RWFWQUX',
	    'PoWFlF RYJlJ R[QjQ RaCaF RZMjMjQ RSHTM RbHbTcVfYk[ RVQRR RaU^YX[ R[QYTkTjXhW RWFWRUX',
	    'PoWFlF RYKlK RaCaG RfWj[ R]P`S R[O^P RaL]P RSIUM RbHbL RWQRS RfNbRZV RiReV_YY[ RWFWRVX',
	    'PoWFlF RYKlK R^RjR R^VjV Rf[j[ R^M^[ RaCaF R]OjOj[ R^MYS RSITM R`H]P RWQRS RWFWRUX',
	    'PoWFlF R_UfU R[YkY R[J[[ R_N_U RaCbF R_NfNfU R[JkJk[ RSHTM RWFWTS[ RWPRR RWOUX',
	    'PoWFlF R`CcF RSHTM RaG]KXM RVQRS R^JhJdN\\Q RaSZV RaRjRgV_Z R^MbOYR R`VeWY[ RWFWRUX',
	    'PoWFlF RZMkM R[QjQ RYVlV RaCaG RbMb[ R]I^L RgHfL RSIUM RWFWTS[ RWQRS RWOVX',
	    'PoWFlF RZJkJ R\\PjP RZUkU RX[l[ RaCaF RbPbZ RgMjQ RWFWTS[ R`J]P RWQRS RSITM RWNUX',
	    'PoWFlF R[JjJ RYOlO RYSlS Rb[f[ RaCaF RbHbO RfOf[ R\\U`X RWPRR RSHTM RWFWQUX',
	    'PoWFlF R\\NhN R\\RhR R\\J\\Z RaCaG R\\JhJhR RSIUM RaRcVfYl[ RWQRS RjTdW RWFWRVX RcYY[',
	    'PoWGlG RZJkJ RWYlY R`J`Y ReJeY RSITM RaCbG RWQRS R[M\\U RiMgU RWGWQVX',
	    'PoWFlF R\\JlJ RXRlR R[XlX Rc[h[ RaCaF RSITM R`NdP R`TdV RVQRS R\\MiMiVh[ RWFWRUY R]H[MZY',
	    'PoWFlF RZJkJ R\\SiS RXZlZ R\\N\\S RaCaG R\\NiNiS RSIUM R^U^Y RfUeY RWQRS RWFWRVX',
	    'PoWFlF RZJkJ R[TkT RY[l[ RaCaF RcTc[ R`OdR R[O^R RfOjS RcL`N RiLfO R]L[N RSITM RWQRS RWFWQUX',
	    'PoWFlF R[JhJ RYNlN RZVlV R^[c[ RbHbN RcTc[ RaCbF RSHTM RWQRR RkIfNXU R\\QiQcT RWFWQUX',
	    'PoWFlF R[RkR R[VkV Rg[j[ R[N[[ RaCaF RcNc[ R[NkNk[ RSITM RWQRS R[IiIbL R^KiN RWFWQUX',
	    'PoWFlF R[J`J RYMlM Rf[k[ RaCaF RcIcR R^SfSf[ RkXk[ R^WZ[ RSHTM RbLeOlR RVQRR RbL_OYR R^S]Y RWFVRUX RhH^I',
	    'PoWFlF RYLlL R^[k[ R^H^Y RaCaF RlXkZ RSHTM RbHeJ R\\SYZ RVQRR RhNaQ RiQaT RkTeW`X RWFWRUX R]L[W',
	    'PoWFlF RZJlJ R\\ZiZ R\\S\\[ RaCaF RcKcR R\\SiSi[ RSITM ReMlP RWFWTS[ RWQRS RdJaMYP RWOUX',
	    'PoWFlF R[RiR R[ViV Re[i[ R[N[[ RaCaG RbHbN R[NiNi[ RfJiKkM R]IYN RSIUM RWQRS RWFWRVX',
	    'PoWFlF RYLjL RdPkP RaCaF R`P[V R`H\\L RSITM RcKdP ReIiJ RWFWTS[ RWQRS R_M\\PYQ R^TaWeYl[ R^ThSdW`YX[ RWOUX',
	    'PoWFlF RZKlK R[RjR R[VjV Rg[j[ R[N[[ RaCaF RbHb[ R[NjNj[ RSITM RfHiI RWFWTS[ RWQRS RWOUX',
	    'PoWFlF RYObO Rf[j[ R^J^[ RaCaF ReKeW RjIj[ R^QaU RSHTM R^NZW RVQRR RbIYJ RWFWRUY',
	    'PoWFlF RZKkK R[PkP RaCaF RbHbP RhUlY RSITM R`SdU RWFWTS[ R[UYZ RWQRS R_U_[gYg[ RWOUX',
	    'PoWGlG RZTjT RWZlZ RaCaG RbIbZ R]M`P RhMlQ RSIUM RhJeQ RWGWTS[ R]JZQ RWQRS RWNVX',
	    'PoWFlF R[SiS ReZkZ R[M[S ReSeZ R[MiMiS RkWkZ R]H_J R`DbF RgHcL RSITM RWFWTS[ RWQRS R_S^W\\YXZ RWOVX',
	    'PoWFlF RZIkI RYRlR R\\ZjZ R\\U\\[ R[MhMgR R\\UjUj[ RaCbF RSIUM RWQRS RaI^R RWFWQVX',
	    'PoVFlF RbTeT RaCaF ReHeT RYN\\O RZI^J RSHTM RiKlQ R\\SYZ RbK_R RVPRR RiRhVeY_[ RVFVRUY',
	    'PoVFlF RYJkJ Rc[g[ RYNYQ R^H^L RaCaF RfHfL RYNkNkQ RSITM RVQRR R`V]YY[ RaP_X R[SiSh[ RVFVRUY',
	    'PoWFlF R[MjM R[QjQ R[I[Q RaCaF RbHbT R[IjIjQ RSHTM RVQRR R^VcZl[ RWFVRUY RZTiTgWbY[[',
	    'PoWFlF RYTlT R\\M\\T RZOjOjT R_IZO RSITM RaCaF RbTfYl[ RVQRS RbT^YY[ RWFWRUX R^KfJcNbV',
	    'PoVFlF R\\PhP Rf[k[ RZLZ[ RbLbZ R_JkJkZ RaPhV RZH]K RaCbF RcP`R\\W RVQRS RSITM RVFVQUY',
	    'PoWFlF R[MiM R[PiP Re[i[ R[I[P RaCaF R[IiIiP R_PYV ReWa[ RbSZZ RfScY RWPRR RSHTM R\\TkSi[ RWFWQVX',
	    'PoWFlF ReOkO R[OaO R[TaT ReTkT R^ZaZ Rh[k[ R[JaJaZ ReJkJk[ RaDcF RSITM RWFWTS[ RWQRS RWOVX R[J[TZY ReJeUcZ',
	    'PoWFlF R[IiI R[MhM R[PhP RXSlS R[I[Z RaCaF RhVdW RSHTM RaTeXl[ RVQRR RWFVRUX RdYX[',
	    'PoWFlF R\\RhR R\\ZiZ RZJZM R\\M\\[ RaCaF RbHbJ R\\MhMhR R\\UiUi[ RZJkJkM RSHTM RVQRS RWFWRUY',
	    'PoWFlF RaCaF R\\SZV RiJfM RiSgV R]JZM RbHdMfOkQ RbQdWfYk[ RWFWTS[ RWQRS RbI`M^OYQ RaV^YY[ RSITM RbP`W RWNUX',
	    'PoWFlF RYL`L ReLlL ReQkQ ReUlU RaCaG ReHe[ R_WZ[ RSHTM RVQRR R`I`UXU RWFWRUX RZQ`P_W',
	    'PoVFlF RYLaL RcLlL RiYlY R\\H\\[ R`O`W RaCaF RfHf[ RjOkWlY RSHTM RVQRR RVFVRUY RYPYX RcPbX',
	    'PoWFlF RYMaM RbMkM R]H][ R`CaF RgHg[ R]OaS RgMlW RfLbV R]MXW RVQRS R]KZT RSITM RWFVRUY',
	    'PoWFlF RZLbL RYQbQ RdYkY RaCaF RdKdZ RdKkKkZ R^K^TbX R\\WX[ RSHTM RVQRS R[HYO RWFWRUY R^O\\W',
	    'PoVFlF RYLkL RXWlW R^[a[ RbUb[ R[IcIbQ R_CbF RSHTM RdNkQ RVPRR R`L^OYQ R\\SgSbU RVFVRUX RhHaI',
	    'PoWFlF R[PiP RXVlV R[K[U R`H`U RdHdU R[KiKiU R`CaF RSHTM ReXlZ RVPRR R_XX[ RWFVRUY',
	    'PoWFlF RYKkK R\\ShS R\\WhW Rb[k[ R\\O\\X RbMb[ R[PiOhW RkXk[ ReKkQ R`CaF R`KYQ RaH^M RWQRS RSITM RWFWRUX',
	    'PoWFlF RZLdL RYOdO Rh[j[ RZSZY R_H_[ RZScScY RfKfV RjHj[ RaCbF RSHTM R[IYN RVQRR RWFWRUX',
	    'PoWFlF R[MjM R[QjQ RYTlT R[J[Q RaCaF RbJbQ RgTg[ R[JjJjQ RWQRS R_T]YX[ RSITM RWFWQUX',
	    'PoWFlF R[NjN R[RjR RXVlV R[J[R RaCaF RdSd[ R[JjJjR RaG`J RSHTM RVQRR RbJbQ_T[U RWFWRUX',
	    'PoWFlF R[MkM R[WhW R[ZhZ R[I[R R[T[[ RaCaF R[ThTh[ RdNhR RSHTM RbJeQ RVQRS RkQkShS RbQZR RWFWRUX RaI[I RgH^I',
	    'PoWFlF R]NhN R_WgW R[ZkZ R[J[[ R_R_V RaCaF RcKcR R_RgRgV R[JkJk[ RSHTM RWPRR RWFWQUX',
	    'PoWFlF R\\MkM R\\QkQ R\\UkU R\\ZlZ R]K][ ReJeZ RbCcF R]LYP RSIUM RdHeL R^H[N RWQRS RWFWQVX',
	    'PoVFlF RaUgU Rf[j[ RZJZ[ RaCaF RaNaW RaNgNgU RaJkJj[ RSHTM R]O_T RZJ_J]P RVPRR R^W\\W RVFVRUX',
	    'PoVFlF RZJaJ RYLlL RYTlT RaCaF RcIcQ RSHTM RdNlQ RVQRR R`NYQ R_S\\WkZ RVFVRUX RhH_I RhUdYY[',
	    'PoVFlF RYLbL R]H]L RaCaF RePhS RSHTM RhHkO RfHcO RVPRR R`[][ RkWcZ RVFVRUX R\\MZX R[QaQaV`[',
	    'PoWFlF RZJkJ RYUlU RaCaF RbHbJ RbSb[ R^NaQ RfNiQ RfNbR R^NZR RSHTM RWQRS RWFWQUX',
	    'PoWFlF RYJkJ R[O_O RdUjU RX[l[ R_J_O R[O[U_U_Z RaCaF RdUdZ RdJdOjOjU RVQRS RSITM RWFWRUY',
	    'PoVFlF RYJkJ RaCaF RdSdY R[Q^S RfRkX RjMfQ RSHTM RcLgT RaJ_LYO RVQRS R_QXT RbR`VXY R^MbPdW RcJeR Rb[^[ RVFVRUY',
	    'PoWFlF RZJkJ RYOlO R\\ViV R\\ZiZ R\\R\\[ R_G_O RaCaF RfGfO R\\RiRi[ RWQRS RSITM RWFWRUY',
	    'PoWGlG R[OjO R[SjS RYWlW R[L[S RbLb[ R[LjLjS R^H_J R`DcF RgIdL RSIUM RWQRS RWGWQVX',
	    'PoVFlF RaKhK R[KcK RXNkN R_DbF ReHiL RUPRS R`G]J RRITM RePkS RaM]QXS RbSZU RdUZX RiVdX[[ RVFVRTY',
	    'PoWFlF RYLkL R[RjR R[UjU RZXjX RX[l[ R[O[U RaCaF RZIcIb[ R[OjOjU RSHTM RVQRR RWFWRUX RhHbI',
	    'PoWFlF RYJaJ RYMaM RYUlU R]H]R RaCaF RcIkIkP RSHTM RVPRR RgQjQfQ RbS`W^YY[ RbVfYl[ RWFWRUY RfIdPaPYQ',
	    'PoWFlF R^[a[ R[P[[ RaCaF R[PaPa[ RiWj[ R\\R_S R\\V_W RgViX R\\IcIgM RdVf[ R_NfLlN RePcU RiQgU RWFWTS[ RaI^LYO RWQRS RSITM RWNUX',
	    'PoVFlF R^NlN R`QkQ R^UlU R\\K\\[ RaCaF RaIiIiM R[MWP RSHTM ReWhZl[ R\\HZO RaO_T RVQRR RfUbY][ RVFVRUX ReQdX',
	    'PoVFlF R]TfT R^YfY R\\ZcZ R]P]T RaCaF RaLaY R]PfPfT RlXl[ ReWgZ RVQRR RSHTM RZIiIiUj[ RVFVRUX RZIZSYZ ReK]L',
	    'PoVFlF RYJcJ RYSbS Rg[k[ RYNYS R^H^[ RaCaF RYNbNbS ReJeV RkHk[ R^RcW R^SXY RSHTM RVQRR RVFVRUY',
	    'PoWFlF R\\LiL R\\OiO RYQlQ R\\I\\O RaCaF R\\IiIiO R^QYW RaU[[ RSHTM RVQRR RWFWRUX R[UjUh[e[ ReUcXa[',
	    'PoWFlF RZJkJ R^MgM R^PgP RYSlS R[ZkZ R[S[Z R^H^S RgHgS RfUiX R`CaF RaU^X RSITM RVQRR RWFWRUX',
	    'PoWFlF RZJgJ RYOlO R]VjV R]ZjZ R^R^[ RaHaO RaCaF R\\SjSj[ RSITM RePhQ RWQRS RiIdOXT RWFWQUX',
	    'PoWFlF RZO_O ReOjO RZTjT RZJZS RaCaF RdJjJjS RSHTM RbHbUfYk[ RVQRR RaU^YX[ R_IZJ RWFWRUX',
	    'PoWFlF R_XeX Rg[j[ RZPZ[ R]I]P R_S_Y RaCaF R_SeSeX R]IhIhP RZPjPj[ RSHTM RVQRR R]LcLcP RWFWQUX',
	    'PoWFlF R[LiL R[RiR R[UiU RXXlX RZIYL R[O[U RaCaF RbJb[ R[OiOiU RYIkIkL RSHTM RVQRR RWFWQUX',
	    'PoWFlF RdQhQ RYUlU R\\K\\T RaCaF RcKdQ RZMjLjS R]IeJbL R^IYN RSHTM RVQRR RbUfYl[ R`L_Q RbS`W^YX[ RWFWRUX',
	    'PoWFlF RZKlK RZQlQ R\\ZiZ R\\T\\[ R_H_M RaCaF RcMcT RfHfM R\\TiTi[ RSITM RWFWTS[ RWQRS RWOVX',
	    'PoVFlF RZOaO RcOkO RZRaR RZWaW RZIZ[ RaCaF RZIaIaO RdIkIkO RSHTM RdUhYl[ RVQRR RcRkRiVfYb[ RVFVRUY',
	    'PoWFlF RZJkJ RYOlO R\\ViV R\\ZiZ R\\R\\[ RaCaF RbHbJ R\\RiRi[ R^K^O RgKfN RWQRR RSHTM RWFWQUX',
	    'PoWFlF RZJkJ RZOlO RZUjU RXZlZ RcScZ R]K^N R`CcF RhKgN RSIUM RcPkS RWQRS RcHbN`Q[S RWFWQUX',
	    'PoWFlF RYLkL RYTlT RaCaF RbHbP R\\I^J RhHfK R_S[W RdMlP RVQRR R`MYP RSHTM R\\WkZ RgUdYY[ RWFVRUY',
	    'PoVFkF RYZjZ RaC`F RbPbZ R\\RiRiV R\\R\\VhWk[ RZL\\N RaJcK RSHTM RVPRR R]J`MdOlQ RZIiIfLaOYQ RVFVRUY',
	    'PoVFlF RXLbL RYTaT RYNYT RaNaU R_CbF R]L]WX[ RfVa[ RSHTM RZH[K RgSjZ ReLkLjP R`H^L RVQRR ReHcP RVFVRUY RgOfW',
	    'PoWFlF R^[f[ R]H]R R^U^Z RaCaF RfXfZ RcIjIkP R`ScV RhUlY RZWWZ RSHTM RVPRR RdPbQ RjQfQ RfIdO R`IYJ RaLYM RbOXP RWFVRUY',
	    'PoWFlF R[MiM R[PiP R[SiS R^[g[ R[J[S R^V^[ RaCaG RgXg[ R[JiJiS RaTdW RhVlZ R[WXZ RbHaJ RSIUM RWQRS RWFWRVX',
	    'PoWFlF RZOjO RZIZO R_I_O RaCaF RbOb[ ReIeO RZIjIjO RiQeU RSHTM RbOdThXlY RVQRR R]RXT R^U[XXZ RWFWRUX',
	    'PoWFlF RYJlJ R\\UhU RX[l[ RaCaF RbRbZ R\\M_P RhNkQ RSHTM RbJdPgSlU RhLfP R\\LZQ RVQRS RaO^SXU RWFVRUY RbHaP',
	    'PoWFlF R[QiQ RW[l[ RZTZZ R[I[Q R_T_Z RaCaF ReTeZ R[IiIiQ RZTjTjZ RaMeN RbK^O RSHTM RVQRR RWFWRUX',
	    'PoWFlF RYJ^J ReJjJ RYM^M ReMkM R\\TgT R\\WgW Rc[g[ R]P][ RaCaF R[QhQg[ RSHTM RVQRR RaH_M\\QXR RbHdMfQjQ RWFVRUY',
	    'PoVFlF R^KfK R\\PiP R[SiS R]ZiZ R]V][ R`C`F R\\NiNiS R]ViVi[ RSHTM RdIlL RaG_JXL RVQRR RVFVQUX R[OZX',
	    'PoVFlF RXRaR R][`[ R]S]W RZJ`J`[ RaCaF RdIiIiOlNlO ReUjZ RSHTM R\\M]O RdSgX R]H\\J RVQRR RcRkRiWfYb[ RVFVRUY RZJZTYZ RdIdO',
	    'PoWFlF RZNiN RZRiR RdXjX RZJZR RaCaF RZJiJiR RbJcRcZlYk[ RiUjX RbGaJ RSHTM RgSeX RVQRR R^S]X[ZW[ RWFWRUY',
	    'PoWFlF R[VjV R[ZjZ R[J[P R[S[[ RaCaF RbSbZ R[SjSj[ R_LaO RSHTM RVQRR RcPaQ ReIdO R`IZJ R`OYP RjPfP RbIjIjP RWFWRUX',
	    'PoWFlF RZKjK R[NjN RYRlR R^UjU R\\[l[ RaCaF RbKbR RcUcZ R^H_K RgHeJ R\\UWZ RSHTM R]R[X RVQRR RWFWRUX',
	    'PoWFlF RZM_M ReMiM RZQ_Q RdQiQ RZJZQ RaCaF RbHbT RdJjJjQ RSHTM RVQRR R_IZI RZTiTfWbY[[ R^VcYfZl[ RcYX[ RWFVRUX',
	    'PoWFlF RaIiI RaRkR R^TjT R\\WlW R^ZjZ R^T^[ RaCaF RbMbR RaGaK RSHTM R[LkLjN RVQRR RWFWRUX R[LZSYY ReO^O',
	    'PoWFlF R[KjK R]PhP R]ShS R]UhU RZWlW RZNZW R\\G\\K R]N]U RaCaF R]NhNhU RSHTM RVQRR ReYl[ R`YX[ RWFVRUX RaI[J RhH_I',
	    'PoWFlF RYJkJ R]RgR R\\ZhZ R\\U\\[ R^G^L RaCaF RfGfL R\\UhUh[ RSHTM RcLeOlR RVQRS RaL^PXR RWFWQUX',
	    'PoWFlF R[KiK R[NiN R[QiQ R\\[h[ R[I[Q R\\U\\Z R[IiIiQ RaCaF RiXi[ RSHTL RVQRS RcSlV RaSXV RfV\\X RWFWQUX',
	    'PoWFlF RbLlL RbPkP RbUkU RbZlZ RcJc[ RhJhZ RSIUL RaCbF R[UWY RYO\\R_X RgHhL R^NYX RcHaN RVQRS RXJ_J[U RWFWQUX',
	    'PoVFlF RbIkI RbLjL RYN^N RbOjO R`SlS RY[][ RYNYR^R^Z RYI^I^N RbIb[ RfVj[ R`DbF RkTgX ReSgX RVQRS RfYa[ RSHTM RVFVRUY',
	    'PoWFlF RYIlI R[PjP R[RjR RYVlV R^[b[ R[L[P R`I`P RaCaF RbVb[ ReIeP R[LjLjP RhXk[ R\\WY[ RSHTM RVQRR RWFWRUX',
	    'PoVFlF RdOjO R`DbF R[KgIiJ RfLcN R_G[J RVQRR RbO[R RaPlS R\\LYO`PXS RbSZU RdU[X RiVdX[[ RSITM RVFVRUY',
	    'PoWFlF RcLlL RYLaL RdPkP RbTlT R]H]L R[Q`Q`Z RaCaF RfWb[ RgSjZ RWFWTS[ RdMcS RWQRS R_[\\[ RSITM RdHbN RWOVX R[L[QZY RgPfX',
	    'PoWFlF RZLjL RZOjO RZIZO RaCaF RbIbO RbVb[ RZIjIjO RYVjUlW RaO^S RSHTM RfSjU R[RaT RfXkZ RVPRR ReQbT^V R\\YYZ RWFWQUX',
	    'PoWFlF RYJlJ R_U^ZgYfZ R\\M^O RaSdW RhVkX RhMkP R`CbF RSHTM RbKcOfRlT RhKfP R\\KZP R[UYZ RVQRS RaN^RYT RbHaO RWFWQUX',
	    'PoVFlF ReTkT Rd[l[ R[O[[ RaCaF RePeZ RbJdM RhKlN R]HXL R\\MWR RSHTM R`TbY RiHfO RbH_O RVQRR R`R^Z RVFVRUY',
	    'PoVFlF RYIaIaN RaCaF RcIjIjO RSHTM RbOlT R^JYL RVQRR R^NYP RgJbL RaPXT RhMdO RcS\\V RdV\\X RfY[[ RVFVRUY',
	    'PoWFlF R[IjI RYMlM R[RiR R[UiU RYXlX R[O[U RaCaF RbGbI RbUb[ R[OiOiU RfIeM RSHTM R^I_M RVQRR RWFWRUX',
	    'PoVFlF R^QfQ RZTjT RaCaF RgWkZ RdLjR R]WYZ ReJkJgO R`LZR RSHTM RcHfO R[K_M R[JaJ_O RVQRR R\\IYM]PXS RbTb[^[ RVFVRUY',
	    'PoVFlF RYKbK RZSbS RZNZS R^H^[ RZNbNbS R]SbX RaCbF R^R\\VXZ RfWb[ RRITM RhKhTjZ RgRdY RVQRR ReLlLkP ReIcQ RVFVRUY RgNfW',
	    'PoVFlF RXJbJ RYSaS RYMYR R]G][ RYMaMaR RgVl[ R\\SaW R_CbF R\\UWY RhVb[ RVPRS RfQiY RiPfX R]Q[V ReLhW RdMkMgV ReHcQ RSHTM RVFVRTY',
	    'PoWFlF RdNkN Rg[j[ RZRZ[ RaCaF RdIdN RZRjRj[ RdIkIkN RSHTM R`RgT RaVhY R[NYO RVQRR RbPaT\\U RbV`X\\Y R]H[N R`N]N RYIaI`N RWFWRUX',
	    'PoWFlF RZM`M RYQaQ RcRiR Rf[j[ R]M]Y RaCaF R]HaL R]HXM RZTZX RSHTM ReHbP R`S_W RVQRS RaYXZ RdLlLkVj[ RWFVRUX',
	    'PoVFlF RZLjL RZOlO Rh[j[ R\\R\\[ RaCaF RcOc[ RZIjIjL R\\RjRj[ ReWhX R_WaX RSHTM RfThU R_TaU RVQRR RVFVRUX RZIZRYY',
	    'PoWFlF RXKlK R[NiN RZSjS RXUlU RZPZS R[I[N RaCaF RbHbS R[IiIiN RZPjPjS RSHTM RVQRR R`S^U\\Xk[ RWFWRUX RhUdYY[',
	    'PoWFlF RYIkI R[PiP R[SiS RYVlV R[M[S R^G^K RaCaF RfGfK R[MiMiS RSHUL RVPRR RbVeYl[ RbSaW_YX[ RWFVQUX',
	    'PoWFlF RYIlI R]LgL R[RjR RZUkU RZXkX RXZlZ R[O[R R]H]L RbLbZ RgHgL R[OjOjR R`CbF RSIUM RWQRS RWFWQUX',
	    'PoVFlF RaPkP R`TkT RYIY[ RcVb[iZi[ R_MkMkT RdUgX R`CcF RbJiJfM RcI^N RSITM R\\N^T RjVkZ RYI^I\\O R_W_[ RVQRR R]V[V RVFVRUY',
	    'PoVFlF RYUlU RZIZO RZI`I`O RdIdO RdIjIjO R^QaR RhQkR R`DbF RgPdS R]PZS RSHTM RVPRR RVFVRUY R]K\\Q RgKfQ R`S\\Xk[ RhUcYX[',
	    'PoWFlF RYIkI R^LfL RXNlN R[TiT R[WiW R[Q[W R^G^L RaCaF RbNbW RfGfL R[QiQiW RSHTM ReYl[ RVQRS R_YX[ RWFWRUX',
	    'PoVFlF R`JkJ R`OjO R`RjR R[N[[ R`L`R R`C`F R`LjLjR RcR^W R\\HXL R]LWQ RaI^L RSHTM RaUdXk[ RVQRR RaUiUgXcY][ RVFVRUY',
	    'PoWFlF RYJlJ R]RhR R^[b[ RaCaF RbVb[ R\\WYZ RjLhN RaI[O RSHTM RdJhNlP RVQRR RZL]NXP RWFVRUX R]OhOhV]V]O RhWk[',
	    'PoVFkF RbQiQ R`UjU RaWjW R_[k[ RYIY[ RaCaF ReQe[ RaS_V RcH_M RSITM RbJeNlP RVQRR RbJiJfN_Q RYI^I\\N^R^U[V RVFVRUY',
	    'PoWFlF R[KkK R[NjN R[QjQ R[UlU R\\J\\U R_W`[ RaCaF RcIcU RhWl[ R]H\\KXN RSIUM RdWe[ R[WZZ RWQRS RWFWQUX',
	    'PoWFlF RYIkI RZL`L RZP`P RdPjP R[WjW R[ZjZ RZLZP R[S[[ R`I`S RaCaF RbSbZ RdIdS R[SjSj[ RdLjLjP RSHTM RVQRR RWFWRUX',
	    'PoVFlF Rc[g[ RYPYS RaCaF RYPkPkS R]K`N RgLjN RcIcK RfKcN RjIiK R[IZK RaI`K RSITM R\\MZN RVQRR R`V^YY[ R^H\\M RaR_X RgHfM RZTiTh[ RVFVRUY',
	    'PoVFlF RZL`L RdLkL RZO`O RdOkO R_TeT R_WeW RcZeZ Rg[k[ RZIZ[ RZI`IaO RaCaF RdIdO R_ReReZ RdIkIk[ RSHTM RVQRR RVFVRUY R_S^Z',
	    'PoVFlF RZS_S RhSkS R[[^[ RYO_O_R RaCaF RkKgL RZI`I[N RSHTM RcNgMlO RcUfYk[ RZK^LXO RVQRR RaUiUfYa[ RhIdJhNhS RcNbS RZRZV_V^[ RVFVRUY',
	    'PoWFlF RZLaL RdLjL R[RiR R[UiU RYXlX RZIZL R[O[U RZIaIaL RaCaF RbOb[ RdIdL R[OiOiU RdIjIjL RSHTM RVQRR RWFWRUX',
	    'PoVFlF R`RkR R_UlU R^ZjZ RYIY[ RaCaF RhWl[ ReHiK RSITM R[O^T RbLgKlM RcUaZ R`OiOgR RYI]I[O RVQRR RdHaK^M R]V[V RVFVRUY',
	    'PoWFlF RZIkI R[L_L ReQjQ RXTlT R^[f[ R_V_[ R_I_L R[L[Q_Q`T RaCaF ReQdT RdIeLjLjQ RaUdW RhWlZ R[XXZ RSHTM RVQRR RWFWRUX',
	    'PoWFlF R]MhM RYSaS RdSlS RZZkZ RYOYS RZVZ[ R]I]M RYOaOaS RaCaF RbUbZ RdOdS R]IhIhM RkVk[ RdOlOlS RSHTM RVQRS RWFWRUX',
	    'PoWFlF RYI^I RfIlI R_LfL RYP^P RfQkQ ReZlZ R\\I\\W RiIiZ RbV^Z RSHTM R`OdX RbHbK R`CaF RcQ`Y RVPRR RdMaW R^VXX RWFVRUY',
	    'PoVFlF RZL`L RdLkL RZO`O RdOkO R_VeV R_YeY Rg[k[ RZIZ[ R_R_Z RZI`I`O RaCaF RdIdO R_RfRfY RdIkIk[ RSHTM RVQRR RVFVRUY',
	    'PoWFlF RZLiL RXNkN R[ShS R[P[S R]V][ R[PhPhS RZHiHiL RhUeW RSHTM RaCaF RaSeXkZ RVQRR R`UXY RZHZL R_H_L RdHdL RbZ\\[ RWFWRUX',
	    'PoVFlF RZRiR R[WhW R[ZhZ RZNZR R[T[[ RaNaR RaCaF R[ThTh[ RZNiNiR R\\O^Q RfOdQ RaHiK RaHXL RVQRR R]KdJlL RSHTM RVFVRUY',
	    'PoVFlF RZKiK RZNiN RZQjQ R\\ShS RZVkV RZIZN R]U][ R_G_N RdGdN RZIiIiN R`DbF RaUdXj[ RVQRS RhWdX RSHSM RcZ\\Z RVFVRUY RZRYY',
	    'PoWFlF R^KfK RZP`P RZR`R R^[g[ RZMZU RaCaF RdNdS RhWlZ RZM`M_UaUdW R[XXZ RSHTM R^W^[ RbGlL RaHXL RVQRR RiMiTfT RWFVRUX',
	    'PoVElE R]McM R]PdP R[O[[ R]I]M R`G`M RcIcM R^ScScXbY RhVd[ R\\HXL R[NXR RgRk[ RjQfY RVQRS RSHTM RfMiX RgGeO RfLkLiV RVEVQUX R^S]Z',
	    'PoVFlF RZIjI RYMkM R[RhR R[UhU R][g[ R[O[T R^V^[ R[OhOhT RaVcX RhWkZ RaCbF RfJeM R]J^M RYWX[ RVQRS RSHTM RVFVRUY',
	    'PoVFlF RXJlJ RbOkO RbRjR RbVjV RbZlZ RaCaF RbGaJ RbNb[ RgMgZ RYN\\RYV R^WY[ R\\KZO RSITM R^N\\R R_S\\Y RcK`R RVQRR R^UXV RVFVRUY',
	    'PoVFlF RbKlK RZO`O RbQlQ RbVlV R[Y`Y RZIZR[T[[ RZI`I`O RZS`S`Y RaCaF RdMdP RgHgK RgQg[ RSHTM RiMiP RVQRR RVFVRUX RZNYX',
	    'PoVFlF RZHiH R[N_N RcNgN R[P_P RcPhP RZViV RZZiZ RYKYN RZSZ[ RaHaQ RaSaZ RZSiSi[ RYKjKjN RaCaF RVQRR RSITM RVFVRUY',
	    'PoWFlF RYJlJ R[OiO R[RiR R\\YhY Rh[k[ RZUZ[ R[M[R R^H^K RaCaF RbMbX RfHfK R[MjMjR RZUkUk[ RfVhY RSHTM RVQRR RWFWRUX',
	    'PoVFlF RZL`L RcLiL RZOiO RZIZO R[Q[Z RaCaF RbQbZ RcIiIiO RdVgW R]R_S R\\V_W RSITM RdRgS RkYl[ RVQRR RaHZI R_YXZ RgY`Z RhRj[ RVFVRUX',
	    'PoVFlF R[PlP R^RjR R^UjU R^ZjZ R^W^[ R^WjWj[ RdNdP R`CbF R^IgIdK R_GZLiLkN RVQRS RbM]N RSITM RVFVRUY R^JZL[SZY',
	    'PoWElE RXIlI R_OeO R[QiQ RZTeT RXVlV R[K[Q RbSb[ R_M_NeMeO R[KiKiQ R`C`F RcWk[ RVPRR RaWYZ RSHTM RWEVRUY RhSbS',
	    'PoVFlF RYMaM RiOlO R[QbQ RZUbU R\\N\\U R`N`U RaCaF RdIiIkNlO ReUjZ R`WbY R\\XYZ RSHTM RdSgX RVQRR RcRkRhWeYb[ RYIaIaM RVFVRUY RYIYRXY RdIdO',
	    'PoWFlF RXIlI R_OfO R[QjQ R[TjT RXVlV R_[b[ R[K[Q RbVb[ R_M_NfMeO R[KjKjQ RaCbE RSITM RgXlZ RVQRS R^XYZ RWFVQUX',
	    'PoVFlF RXKaK RYSaS RYNYS R]H][ RYNaNaS RaCbF RdNdW RdNkNkW RiXl[ R]SaW R]SWY RgWc[ RVPRS RSHTM ReJjJgN ReHcM RVFVRTY RgQgX',
	    'PoVFlF R[KiK R[MiM R[OiO RZXjX Rg[j[ RZUZ[ R[I[O R_U_[ RaCaF RdUd[ R[IiIiO RZUjUj[ RSITM RaOhS RVQRR R^SdRlT RcO]RXT RVFVRUY',
	    'PoWFlF RZIlI RZXiX R[TiSi[ RcGcI RiLkP R`DbF RSHTM R\\LZQ RVPRR RiPeP RfMfQ RkKfM RZL_L_Q]P ReKcMbQ RWFVRUY R[RZY',
	    'PoVFlF RZN`N RZR`R RfTkT RYUaU R[H[N RaCaF R]V_Y RbIjIgL R]WX[ RcKhM RbOkOjR RZOZS RVQRR R_IZK RSHTM RcRaZ RgOfZk[ RVFVQUY R]Q\\X',
	    'PoVFlF RYIjI R[KiK RXNkN R_PeP R\\TgT R\\VgV R\\R\\Z RaC`F RbIbM R\\RgRgV RSHTL ReNhPlR RVPRR R^N[QXR RiWcYl[ RVFVQUX RbYY[',
	    'PoVFlF RcMlM RYQaQ RcQkQ RYUaU RcUlU RZLZU R^M^U RXNaMaU RgMg[ R`CaF RdHeK RjHiK R[HXN R[J_J]O RVQRR RSITM RVFVRUY RaXXY',
	    'PoWFlF R[JbJ RdJlJ RZQaQ RZTaT RhXkX RZ[b[ RZNZS R[U[Z RaCaF RZNaNaS ReNe[ ReNkNkX R]I^L RfIhL RSHTM R[GYL RdGcL RWQRR RaVZW RWFWQUX',
	    'PoVFlF R]MdM R]OdO R]RdR R]UdU R[N[[ R]I]M RaHaM RaCaF RdIdM RhVd[ R\\HXL R\\MXQ RhSkZ RjRgY RVQRR RfNiY RgHeQ RSHTM RgLkLiW RVFVRUY RaRbY]Y',
	    'PoWFlF R[MjM RZOjO RYRkR RZWjW RW[l[ R[H[M RaCaF RbHbR RbVb[ R[HjHjM R^I_K RiSkV R\\SYV RfIeK RSITM R`S_U RdSeV RVQRS RWFWQUY',
	    'PoVFlF RZIkI R\\ObO RdOlO RcWjW RZ[l[ R^U^Z R`L`S RaCaF RcTcZ RhLhS R^ObR RhNlR R_O[S RgNcR RSHTM RVQRR RaK\\L RjKdL RVFVRUY RZIZSXZ',
	    'PoVFlF RZHjH R[M`M RdMiM R[O`O RdOiO R[SkS R[UjU R[XjX R[ZkZ RYKYN R\\R\\[ RaCaF RbHbO RcQcZ RYKkKkN R]QYU RSHTM RVQRR RVFVRUY',
	    'PoVFlF RXJaJ RcRkR RYR`R RcTkT RYNYR R]G][ RYN`N`R RaCaF RcOkOkW RcOcWjXl[ R\\R`W R[TWY RfKcN RSHTM R\\QZW RVQRR ReYa[ RcIkIkMhM RVFVRUY',
	    'PoVFlF RaIkI RaLkL RXN_N R`NlN RbSkS RbUkU RbWkW R\\I\\[ R`CaF RbPbW RfHfN RbPkPkW R[O_S RSITM RhYlZ R\\NWW RVQRR RdY`[ R_IXJ RVFVRUY',
	    'PoWFkF RcIlI RXIaI RYO`O RYR`R RYT`T RWWbW RYLYW R\\HZL`L`W RdLdW RhIfL RgOgW RdLkLkW RiXl[ R_XaZ RZXX[ RfWc[ RSIUL RaCbF RVQRS RWFVRUX',
	    'PoVFlF RcMlM RYQaQ RcQkQ RYUaU RcUlU RZLZU R\\W\\Z R^M^U RaCaF RYNaMaU RgMg[ RaVbY RSITM R_W_Z RdHeK RjHiK R[HXN R[J_J]O RYWX[ RVQRR RVFVRUY',
	    'PoVFlF R[KaK RdKjK R[MaM RdMjM RYTlT RaCaF R[IaIaO RdIjIjO RdIdOjPkR R`S[X ReQbR R[QYR RSHTM R[I[O`PaQ RVPRR R\\Xk[ RVFVRUX RhTdYX[',
	    'PoVFlF RaLjL R_NlN R_SlS R`UjU R[W]W Rb[h[ RYIY[ RaCaF RbWb[ ReLfN R`PjPjU RcVfW RiWlZ RiIlL R`X]Z R`I_M RSITM RVQRR RYI]I\\O]U RVFVRUY RjHaI',
	    'PoVFlF RZKaK RcKjK RZMaM RcMjM RZOaO RcOjO R[SjS R[UiU R[WiW R[ZkZ RZIZO R\\R\\[ RZIaIaO RaCaF RcQcZ RcIcO RcIjIjO R]PXU RSHTM RVPRR RVFVRUX',
	    'PoVFlF RdMlM RdQlQ RYQ`Q RdUlU RYWaW RdZlZ RY[b[ RYSYZ RYNYQ R]S]W RYN`N`Q RaCaF RYSaSaW ReKe[ RiKiZ R\\K]L RYJZM R_JaL RYHYJ R]H\\J RaH_J RfHbP RVQRR RSHTM RiIhO RVFVRUY',
	    'PoVFlF RXJbJ R[L`L RdMlM RdQlQ RYRaR RYTaT RdUlU RXWbW RdZlZ RYNYQ R[H[L R`H`L RaCaF RYNaNaQ ReKe[ RiKiZ R]L]XZ[ R]XaZ RiHhN RVQRR ReHdP RSHTM RVFVRUY',
	    'PoVFlF RcIlI RZLaL RdOkO R[PaP R[RaR RdSkS R[UaU RdVkV RZHZL R[N[U RaCaF R[NaNaU RdLdV RgIeLkLkV RiXl[ RYOYWaXaZ R[XX[ ReXc[ RVQRR R`IYJ RSHTM RVFVRUY',
	    'PoWElE R[KjK RdQlQ R[TeT R^[b[ RbWb[ RYW]WdWiVkX RSHTL RfYlZ RVQRR RYQaR[T R]YYZ RgIjI R]M]PaO R[G[J^I[I R_IbI_I RbGbJeIbI R[GjGjJfI RYMYQ RgSYW RZO[NaMaP]OYO RdMlMkPhOlO RhMgPcNdPhO RWEWQUX',
	    'PoVFlF R`HdH R_JdJ RXM^M ReMlM R_QdQ R[SeS RYUjU RXXlX R][a[ R[M[R R_O_R RaCaF RbSb[ R_OdOdR RiMiR RgGgJ RjIfM RZGYJ R\\IYM R]O]Q RkNkQ RXOXQ RVQRR RSITM RaMdM RbM_M RVFVRUY RgSaS',
	    'PoVFlF R_IeI R_OdO Rf[k[ RZTZ[ RiOiR RZTkTk[ RaDcF RiGfJ RjIfN R[GXJ R]IXN R]O]Q RkOkQ RbWhY RfOfR RXOXR RVQRR RcT\\W RbW]Z RaThV R\\JXK RjJfJ RSHTM RbMdM R]MXM RbM_M RkMfM R_O_RcQbT RVFVRUY',
	    'PoUK]K ReTlZ RTNYTR[ RhJcO RkOfT RaJgW R]LXV',
	    'PoUE^E RTSjS R_M_[ RUGYJ RbGhM RjGgJ RgCcG RaCcH RWMgMlN R]FWLRN',
	    'PoTSjS RTHXJ RaFiN RiIeK RkGhJ R_RaV RgDdG R`CbH RXMfMlO R_M`TdXj[ RTE]E[HVMRO R_T[XT[',
	    'PodPkP RUSgS RUGXI RjFgI RgDdG R`CeIlL RZKYNSP RUD]DYIRL RZKdKdP RWUj[ RfT_XT[',
	    'PoUE^E RTSjS Rc[j[ RcMc[ RjWj[ RUGYJ RbGhM RjGgJ RgCcG RaCcH RWMgMlN R]FWLRN R[M[VXYT[',
	    'PoTE]E RZKdK RXUfU RS[l[ RXOXU RXOfOfU RUGYJ RjGgJ RZV\\Z RaCbG RgDbGhMlN RdVb[ R\\FVMRN',
	    'PoUQ\\Q RgQjQ RU[Z[ RUL\\L\\Q R`LgLgQ RiHfI RUE]EXJ RgDdF R`TdXj[ R`DeIlL R_TiTeX_[ RUFYIRL R`L_Q RUPTU\\U[[',
	    'PoRQlQ RRYlY RRHR[ RRHlHl[ R^C\\H',
	    'PoRElE RVRiR RVYiY RVKV[ RVKiKi[ R_E]K',
	    'PoRO[O RRW[W R`[k[ RRHRY RRH[H[V R`D`[ RWCUH RlVk[',
	    'PoRJkJ RROkO RS[k[ RRFRO RTQT[ RRFjFjO RlXj[ R^C[F RZURV RhRZU',
	    'PoUKiK RUPiP RRUlU RUFUP R_P_[ RUFiFiP R^C]F',
	    'PoVJiJ RVOiO ReTkT R^TeT RZUaU RSUZU RZ[i[ RVFVO RZPZ[ R_C^F RVFiFiO RjXi[ RgSbT R\\TWU',
	    'PoUKiK RUQiQ Rc[k[ RUFUQ RcQcZ RUFiFiQ RlVk[ R^C]F RZVWYR[ RZQYX',
	    'PoRP\\P RRY\\Y Rd[j[ RRHR[ RRH\\H\\Y RbNgT RWCUH RcC^M RaHlHlRj[',
	    'Po]OkO RROZO RRWZW R^[k[ RRHRY RRHZHZW R^E^[ RdEdO R]EkEkQ RlWk[ RVCTH',
	    'PoUG^G RaNlN RWUhU RWZhZ RUCUM RWQW[ RbCbM RWQhQh[ R^N]Q RlKkM RjFaH R[MRN R^LTN',
	    'PoUJiJ RUOiO RTSjS RUWiW RR[l[ RUFUO R_S_[ RUFiFiO R^C]F',
	    'Po^DkD RROZO RRVZV RRGRY RRGZGZV R_LcThYl[ RVCTG R^LkLgTbY^[ R^D^Q\\X',
	    'Po_McM RROXO RRVXV Re[l[ RRHRY RTCSHXHXV R[J[Z R_C_Y RfCf[ RlWk[ RkKeN RcXXZ',
	    'PoROYO RRVYV RRHRY RRHYHYV R_Ue[ RcKdN RcDkM R]PjPcX RcCaG[M RVCTH',
	    'PoVIiI RVMiM RRQlQ RWVgV RVFVM R_S_[ RVFiFiM R_C]E RcQfTlV R]OXTRV',
	    'Po[HkH RROYO R\\OkO RRVYV RRHRY RRHYHYV RcCc[ RfSlY R`SYZ RdOgU RcMaR RUCUH',
	    'PoVJjJ RVMjM RUS_S RhYlY RUOUY RVFVM R`D^F RbPb[ RVFjFjM RbPlPlY R_XRY',
	    'Po[HlH RROYO RRVYV RRHRY RRHYHYV RcCcG RgJkN R`J[N R_OaTfXl[ RgNeTaXY[ RUCUH',
	    'Po\\IkI RROYO R[QlQ RRVYV RRHRX RRHYHYV RgIg[ R^C`H RUCUH RhCfH R\\Z[[ R`I`R_X',
	    'PoUJiJ RUNiN RTP[P RbPjP RSS[S RbSkS RRWlW RUEUM R^C^F R_O_[ RUEiEiM',
	    'Po\\JkJ RROXO R\\TjT RRWXW R\\[k[ RRHRY RRHXHXW R\\D\\J R]T][ RcCcJ R\\NjNjT RkDkJ RlXk[ RUCUH',
	    'PoZLlL RROXO RRVXV R[[_[ RRGRX RTCSHXGXV R_F_[ RhEkH RhSaZ RjOgU RcEZG RcQZT ReDeNgY RlVk[i[',
	    'Po]IkI R[NlN RROYO RRVYV R]ZjZ RRGRY RRGYGYV R]R][ RcCcN R]RjRj[ RVCTG R^D\\L',
	    'Po\\IjI R\\MjM RROYO R\\PkP RZUlU RRVYV RRGRY RRGYGYV R\\D\\M RcPc[ R\\DjDjM RVCTG',
	    'PoRE^E R`ElE RSR]R RaRkR RSZ]Z RaZkZ RSKS[ RSK]K][ RaKa[ RaKkKk[ RXEWK RfEdK',
	    'Po^LiL RROYO R[QlQ RRVYV Rf[l[ RRGRY RRGYGYV R[G[J RcCcG RfQf[ R[GkGkJ RlXl[ R_V\\ZY[ R`Q^X RUCUH',
	    'Po`JiJ R_NiN RROYO RRVYV R`WhW Rg[l[ RRGRY RRGYGYV R`R`X RdGdN R`RhRhW R]ElEl[ RVCTG R]E]Q[X',
	    'Po\\EkE R]IkI R[LlL RROYO R^SjS RRVYV R^VjV Rf[j[ RRHRY RRHYHYV R^O^[ RcCcL R^OjOj[ RUCUH',
	    'PoSG]G R`IlI RWVhV RWZhZ RWRW[ RXCXP R`E`L RgIgP RWRhRh[ RXH[K R_P^Q RWIRN RXEUK RjD`E',
	    'Po\\IkI R[LlL RROYO R]UjU RRWYW RZ[l[ RRHRY RRHYHYW R\\E\\I R]O]U RcCcI R]OjOjU RkEkI R_V`Z RhVfZ RUCUH',
	    'PoVJhJ RVNhN RRV]V RaVlV RRZ]Z RaZlZ RRRR[ RVFVM RWOVR RRR]R][ RaRa[ RVFhFhM RaRlRl[ RfOeR R_C]F',
	    'Po[FlF R^MiM RROYO RRVYV R`XgX Rh[k[ RRHRY RRHYHYV R\\Q\\[ R^I^M R`T`Y RcCcF R`TgTgX R^IiIiM R\\QkQl[ RUCUH',
	    'PoTJ]J RTN]N R^NlN RSQ]Q RSU]U Rg[l[ RTFTM RXQXY RTF]F]M ReDeN RgNgZ RlWl[ RXCXF R`FaK RjEhK R_Z][ RbNaX R^XRZ',
	    'Po\\IjI R\\MjM RROYO R[PlP R]ViV RRVYV RRHRY RRHYHYV R\\F\\M RbCbF RcRc[ R\\FjFjM RbM`Q[U RePhTlU RUCUH',
	    'Po\\EkE R]HjH R[KlK RROYO R]QjQ R]TjT RRVYV R]WjW RRHRY RRHYHYV R]N]W RcCcK R]NjNjW RfXl[ RaX[[ RUCUH',
	    'Po\\IkI R]NlN RROYO R]RkR R]UkU RRVYV R]ZlZ RRHRY RRHYHYV R\\D\\I R^M^[ RcCcI ReLeY RkDkI R]NYR R^J[P ReJdP RUCUH',
	    'Po]JjJ R]NjN RRNXN R\\QaQ RfQjQ R\\TaT RfTkT RRVXV RZWlW RRHRX RRHXHXV R]F]N RbCbF RcOc[ R]FjFjN RUCUH',
	    'Po\\FjF RZIlI R[MaM RdMkM RROXO RcQlQ RZTlT RRVXV Re[l[ RRHRY RRHXHXV R^K^Q RbCbI RfTf[ RgKgQ RlXl[ RUCUH R`T^X]YX[ RaPZQ',
	    'PoZFlF R[JkJ RZNlN RROXO RZSlS RRVXV R[WkW RRHRY RRHXHXV R^J^R R`C`H RcJc[ RfCfH RhJhR RUCUH',
	    'Po[JkJ RROYO R]VjV RRVYV R]ZjZ RRHRY RRHYHYV R]Q][ R\\EcEdP RcQcZ R]QjQj[ RhFgI RcJ]OZQ R^G_I RcJhOlP RUCUH RiC_E',
	    'Po]HjH R]KjK R]NjN RROYO R\\QaQ RfQjQ R\\TaT RfTkT RRVYV RZWlW RRHRY RRHYHYV R]F]N RbCbF RcOc[ R]FjFjN RUCUH',
	    'PoZJbJ RZMbM RROXO RYQcQ RRVXV R\\[`[ RRGRX RUCSHXGXV RZFZM R]C]F RZFbFbM RgUb[ RfQjZ RiOeX ReLhX ReIkIgV R^N]QZY RfDcO ReIgU R[UaT`Z',
	    'Po[EcE RROXO R`QgQ RRVXV RRHRY RRHXHXV R[J[M R[JkJkM R^G^J RaLdM RhMkP R`R[W R]M\\P RcFcI RiFgJ R`M`Q R^TaXeYl[ R^ThTeXaYZ[ RUCUH RiDbD',
	    'PoWHgH RWKgK RTQ]Q RaQjQ RTT]T RaTjT RUWjW RR[l[ RTNTT RWEWK RTN]N]T R_U_[ RaNaT RWEgEgK RaNjNjT',
	    'Po`HkH RTJ\\J R`LkL RTN\\N RSQ]Q R_SlS RSU]U Rg[l[ RTFTM RWCWF RXQXY RTF\\F\\M R`D`L ReLeS RhSh[ R`DkDkL RlYl[ RbW_[ R`NaQ RjNhQ RcSaY R]XRZ',
	    'Po`KeK RROXO RfPkP RYP_P RYSlS RRVXV RRGRX RUCSHXGXV RZH\\L R`F`PdPb[ RbCaFeFeP RgHhL R^M_P RjMlP RbSgWlY RjGgO R\\D[I R^HZP RhDgI RbS^XYZ',
	    'Po[FlF R[MkM RROWO R]PcP ReTlT RRVWV RRGRX RRGWGWV R]N]U R`F`M RaWa[ RfNfT ReFeM R[IkIkM RjWk[ RcCdF ReWf[ RUCTG R\\WZ[ RkPeQ RcS\\T R[FZQYZ',
	    'Po\\DkD R]JaJ ReJjJ ReMkM R\\MaM RROYO R\\RlR R]TkT RRVYV R]WkW R]ZlZ RRHRY RRHYHYV R[G[J R]P][ RcDcM RdPdZ R[GlGlJ R^PYU RUCUH',
	    'Po[MjM RROWO R[RbR RcRlR R[UbU RRVWV R[WbW R[YbY Rf[i[ RRGRX RTCSHWGWV R[P[[ R[H[M R[PbPbT RiOi[ RZJjJjM ReTfX RhElI R\\EbEbH R\\EZJ R`J`M ReJeM RhD`E',
	    'Po`C`N RVGjGhL RZN[R`WeYl[ RVNhNgRbV\\YV[ RVGVQTY',
	    'Po`EkE R_OlO RYCYM RTG^G]K RfEf[ RVP\\W^Y R[UU[ RTN^NZW RTGTTR[',
	    'Po`RfR RfVjV R`[k[ RYCYN RTH]H\\L R`J`Z R_LfLfR RlWk[ RVQZU\\Y RZVU[ RTN\\OXX RbD^M RaHkHkPjU RTHTTR[',
	    'PoTS]S RTY]Y RTM]M][ RfCfN ReUk[ RhUb[ RbNfW RaNkNgW RXCTL RWG]GYM RaHkHkK RaGaS_[',
	    'PoSJ^J RSQ]Q RUV[V RRWYW RRERH RYMY[ RRE^E^H RfCfN RdTjZ RhTbZ RbNfW R`NkNgW RXGSQ R]UWV R`HkHkK R`G`R_Y',
	    'PoRH^H RT[X[ RXCX[ RfCfN R`HkGjK RdTjZ RZR^V R[D]F RVSSV RTKUP RaNeW R]K[P R`NkNhTcY_[ R`G`Q_Y',
	    'PoRI^I RZN^N RZHZM ReCeN R`HkHkK RdUjZ RYU\\X R[F^I RVKSN RWPRU RbNfW RWCUHUM RVQ\\QYW RSR[UVYS[ R`NjNgVdY`[ R`H`R_X',
	    'PoRG]G RRM^M RTU\\U RTZ\\Z RTQT[ RUCUM R[C[M RTQ\\Q\\Y ReCeN R`HkHkL RdTjZ RhTbZ RbNeV R`NkNgV R`G`R^Y',
	    'PoSG^G RTR]R RTU]U RTNTY RXDXM ReCeN R`HkHkK ReUj[ R[I^L RYGRM RbNfW RXH\\J R`NjOgVdY`[ R`H`R_Y RZYTY RTN]N^XXY',
	    'PoRF^F RSJ]J RTS]S RTMTR RXCXJ RTM]M]R ReCeN RUTVX RbOeViZl[ R`HkHjL R[TZX R`OjOhVdY`[ R`H`R^Y R]XRZ',
	    'PoSI^I RSP]P RSS]S RRV^V RSESH RSLSS RXGX[ RSL]L]S RSE^E^H ReCeN RdTk[ RbNfW RaNkNhVdY`[ RaHkHkK RaHaR_Y',
	    'Po_JkJ R_PkP R_TkT R^WlW RXCXN RTH]H]L R_E_G R_M_S ReGe[ R_MkMkS R_DkEkG RVQZU\\Y RZUU[ RTN\\OXX RTHTTR[',
	    'PoTK[K RTW[W RY[][ RTTTW RTHTK R[H[K R[T[W RfCfN ReUk[ RXWT[ RhTb[ RUCSH RbNfW RaNkNgW RWKUNSS R]NZN RWSWY RaHkHkK RWGWM RTF^F]N RTR^Q^U]Z RaHaS_[',
	    'PoYE^E RXN^N RbNjN RWS]S RWV]V RWPWZ RYCYM RfCfN RbHkHjK RfUl[ RhVc[ RcNgW RjPfX RTH_H_J RWP]P^YUZ RTHTTR[ RbHbS`[ R\\JVK',
	    'PoRF^F RSI]I RTN\\N RTU]U RTQTU RXCXI RTQ]Q]U RRLRM^L^N RfCfN RaHkHkK ReUk[ RVVVY RbNfW RaNjNgVdY`[ RaHaR_Y RZVZYRZ R\\YTZ',
	    'PoRF_F RVM[M RSO^O RTT]T RTW]W RWZ^Z RSZZZ RSHSO RTRTW RVKVM RXDXF RVK[K[M RTR]R]W RSH^H^O RfCfN ReUj[ RhTb[ RbNfW RaNjNfW RaHkHjL RaHaS_[',
	    'PoRE_E RSH^H RSN^N RTR]R RTU]U RTW]W RSJSM RTPTW RVJVM RYCYH R[J[M RTP^P^W RSJ^J^M ReCeN RdUj[ RZY]Z RVYR[ RbNfW R`NkNhVdY`[ R`HkHkK R`H`T_[',
	    'PoR[l[ RUGU[ R\\G\\[ RcGc[ RUGiGi[',
	    'PoR[l[ RVRV[ R\\R\\[ R^C^P RbRb[ RVRhRh[ R^HgL',
	    'PocPiP RR[l[ RVSV[ R\\S\\[ RbSb[ RVShSh[ R[HYNUQRR R[EYM RTEdEcJjIiP',
	    'PoVEhE RSKlK RZQ_Q RR[l[ RVSV[ R\\S\\[ R_E_P RbSb[ RVShSi[',
	    'PoSEkE RR[l[ RVRV[ R\\R\\[ R`G`Q RbRb[ RVRhRh[ RaJlN RaE\\KRO',
	    'PoVIiI RdQjQ RR[l[ RVTV[ R\\T\\[ R_E_R RbTb[ RVTiTi[ RTEiEiI RUIUMkMjQ',
	    'PoUMiM RR[l[ RUFUN RUSU[ R\\S\\[ R_C_R RbSb[ RUSiSi[ RUFiFiN',
	    'Po`QeQ RR[l[ RUSU[ R\\S\\[ RbSb[ RUSiSi[ RdCiIlJ R]J[NYPUQ R[CVHRJ RXJfJeQ',
	    'PoRHlH RR[l[ RVSV[ R\\S\\[ RbSb[ RVShSh[ R]N_Q R_G`JdNkQ R_F\\KYORQ R_D]K',
	    'PoYJaJ ReQiQ RR[l[ RVTV[ R\\T\\[ RbTb[ RVTiTi[ RWMRR RZLaO RYHUP R`K]OXQ RYEWN RSEeEdKkJjQ',
	    'PoRJlJ RR[l[ RVSV[ R\\S\\[ RUF\\F`E_Q RbSb[ RVShSh[ R`JcNlQ R^JYNRR RhD^E',
	    'PoSIkI RR[l[ RWSW[ R\\S\\[ RbSb[ RWShSh[ RfCeFcH RXDZH R[IYMSS RcIeLkR',
	    'PoRHlH RR[l[ RUTU[ R\\T\\[ RbTb[ RUTiTi[ RZLSR RbDgF R\\EXN RfJbOYR R[HaNfQkR',
	    'PofLiL RbQkQ RR[l[ RVTV[ R\\T\\[ RbEbQ RbTb[ RVThTh[ RaEjEjL RWJ[M RUIYK RkNkQ RXERK RWG^G\\MXQSR',
	    'PoUFiF RRKlK RR[l[ RVTV[ R\\T\\[ R_C_K RbTb[ RVTiTi[ RSQiPkR R\\KZMWP RdMhO',
	    'PoRLkL RR[l[ RVTV[ RVFVL R\\T\\[ RbTb[ RVFhFhL RVThTh[ R^L[PSS R_C_LcPfRlS',
	    'PoaGhG R\\GeG RcKkK RaKhK R^KfK RR[l[ RVUV[ R\\U\\[ RbUb[ RVUhUh[ RTHZH]G_N RSLXL^KbPgR RaDdE R\\D^L RkOkRgR RaK\\K RiLeN`PTR',
	    'PoSH_H RR[l[ RUSU[ RYCYO R\\S\\[ RcSc[ RcCcQ RUSjSj[ RdHlL R^NRP R`NTO',
	    'PobHlH RR[l[ RUDUO RVSV[ RZCZP R\\S\\[ RbSb[ RVSiSi[ RdLiN RcC_M',
	    'PoYIeI RWQgQ RR[l[ RVTV[ RWLWQ R\\T\\[ RbTb[ RWLgLgQ RVTiTi[ RaElJ R^C[FRK',
	    'Po\\EkE R[JlJ R_QdQ RR[l[ RUSU[ R\\S\\[ RcSc[ RdEdQ RUSiSi[ RWNSQ RYLUP RSIXJ RTDYE',
	    'PoRElE RR[l[ RVTV[ R\\T\\[ RbTb[ RVThTh[ RhHfL RbMdPjR R[IYM R_M\\QWS R`GbN R`G_O RVCVKUR',
	    'PogQlQ RR[l[ RVTV[ R\\T\\[ RbTb[ R^EgEgQ RVThTi[ RlNlQ R`IdM RYLTR R]NYR RTDYE RSHWJ R^E]J]O',
	    'PoTGjG RUKiK RSPkP RR[l[ RUSU[ R\\S\\[ R_G_O RbSb[ RUSiSi[ RYCZF ReDcG',
	    'PoR[l[ RVSV[ R\\S\\[ RbSb[ RVSiSi[ RSEXG R^C\\I RXMRP R]GkGiK RcGaL_PYQ RcHdLgOlQ',
	    'PoRLlL RR[l[ RVTV[ R\\T\\[ RbTb[ RVThTi[ R^N`P R]G`J R^C^F RUQSR RXFgFgQdR RXFWKWP',
	    'PoSFjF RVLiL RVOiO RdRhR RR[l[ RUUU[ RVIVR R\\U\\[ R_C_R RbUb[ RVIiIiR RUUiUi[ RcDgE',
	    'PoRGkG R[R_R RR[l[ RVTV[ R\\T\\[ R_C_R RbTb[ RVThTi[ RbMkQ RUI[J RhIbK RcDiE R\\MSP',
	    'PoUGkG RXQ\\Q RR[l[ RVTV[ R\\T\\[ RbTb[ RVThTh[ RcMhR RdCfE R`GdO RiIcO^Q R`DcM RkOkRhR RUK]K\\P RUGULTR',
	    'PoR[l[ RUSU[ R\\S\\[ RbSb[ RUSiSi[ RXLSQ RcFdLgOlQ R]GkGiK R^C[K RcGbL_OZQ RTDXE RRHWH',
	    'PoVGgG RRJlJ R[S_S RR[l[ RUUU[ R\\U\\[ R_J_S RbUb[ RUDgDgJ RUUiUi[ RUMZN RiMcO R_MfQlR R_NSR',
	    'Po`DeD RZFiF RUFdF RXNjN RVNiN RTNgN RR[l[ RVUV[ R\\U\\[ RbUb[ RVUhUh[ RjIjK RkQlT R_LdL R\\D`HcJiK R\\L`QdSjT RfHUK RfPVS',
	    'Po`HjH RTJ\\J R`MjM RTN\\N RfRjR RR[l[ RTETP RUTU[ RTE\\E\\N R\\T\\[ RbTb[ RUTiTi[ R`DjDjQ R`D`K_P',
	    'PoXHeH RSJkJ RR[l[ RUUU[ R\\U\\[ RaOaS RbUb[ RUUiUi[ RaLfQlS RZCXH RiLcN RYEfEdJ R`JSN R^NTQ R]K`MaP[RST R`T\\T',
	    'PoRHlH RULhL RSOkO RR[l[ RVUV[ R[Q[S R\\U\\[ R_C_O RbUb[ RUEhEhL RVUhUh[ RaPbS RgQkT RVQST',
	    'Po_IkI RTK]K RTP]P RR[l[ RTETP RUTU[ RXEXP R\\T\\[ RTE]E]P RbTb[ RUThTh[ RgMkR RgEjG RcM_Q ReHhP ReDdM',
	    'PoSD^D RbHlH RSL^L RbOkO RSP^P RR[l[ RSDSP RUTU[ R\\T\\[ RbTb[ RUTiTi[ RcD`L RYLYP RYEYH RSH^H]M',
	    'PoRL^L RR[l[ RVTV[ RYMYP R\\T\\[ RbTb[ RVThTh[ RXGYJ RbMePkS RbDhDhJlIlJ R`LjLfP`S RUE]E]RZR RbDaJ RUEULTQ RYDXE',
	    'PoTJXJ ReJjJ RTPYP RePjP R\\Q_Q RR[l[ RTETR RVSV[ R\\S\\[ R_D_Q RbSb[ RVShSh[ ReEjEjR RaJeN RdGaJ R_EbM R]HZN RYH]H RZDSE',
	    'PoSLkL RaPiP RURiR RUTiT RR[l[ RUWU[ RUNUT R\\W\\[ RaLaP RbWb[ RUNiNiT RUWiWi[ RWJeIeL R_ChH RZGbFlH R]L\\PWQ R_CXGRH',
	    'Po_EjE R[KfK RYKdK R_MlM RYRhR RYThT RU[l[ RWWWZ RYOYT R]W]Z R_C_L R`O`T RcWcZ RYOhOhT RWWiWiZ RUHkHjJ RUHTSSZ',
	    'Po\\ElE R^JjJ R^MjM R^OjO RR[l[ RVUV[ R\\U\\[ R^G^O RbUb[ RVUhUh[ R^GjGjO RdEcG RfQlS RZORS RaQ[S RXFRH RYJSM',
	    'PoSF^F RRI_I RSM^M RRP_P RR[l[ RVTV[ RYMYS RYCYI R\\T\\[ RbTb[ RVThTi[ R\\J[M RVJVL RcHeNhQlR RdC`K RbHkHgNdQ`R',
	    'Po\\GiG R\\JiJ RZLlL RfTiT RR[l[ RVVV[ R\\V\\[ R\\D\\J RbVb[ RVVhVh[ R\\DiDiJ RaN\\T RePaT R^LYQ RUPTT RTDXE RRIVJ RWMUR R\\OjOiT',
	    'PoWKgK RTMkM RaQiQ RUSiS RR[l[ RUWU[ R\\W\\[ RaMaQ RbWb[ RWIWJgIgK RUWiWi[ RYGcFlH R^DbD R]M\\QWR R_CYFRH RbUeU R[UXU R`UcU R\\U_U RUOUUYU\\U RbU^U[U RUOiOhUeUbU',
	    'PoTE^E RaFlF RaIkI RTM^M RTQ_Q RaQkQ RR[l[ RTETQ RVTV[ R\\T\\[ RTI^I^M RaMaQ RbTb[ RfGfM RVThTh[ RaMkMkQ RbC`I RYMYQ RYEYI',
	    'PobHlH RTL^L RRN_N RTQ^Q RSS_S RR[l[ RUWU[ RYKYN RYQYU R\\W\\[ RbWb[ RUWhWh[ RTIZI]H_J ReOjT RWCTG RiIfQ RdC`K RbHgPcS_T R[FTG',
	    'PoRLlL RRSlS RRYlY RRER[ RRElEl[',
	    'Po\\FlF RRKYK RRQYQ RRWYW R_[e[ RRERY RREYEYV RfFf[',
	    'Po\\EkE RRKYK R[OlO RRQYQ RRWYW RRERY RREYEYV RcEc[',
	    'Po\\EkE RRKYK R[NlN RRQYQ RRWYW R_[c[ RRERY RREYEYW RdEd[',
	    'PoRFlF RVKjK RVRhR RVVhV RVZhZ RVFVK RVOV[ R_C_F RVOhOh[',
	    'Po\\JlJ RRKYK RRRYR RRXYX R_[k[ RRER[ RREYEYX R_J_[ RbEeH',
	    'PoRFlF RYNhN RYRhR RYVhV RTZlZ RTKT[ RYJYV R_C^J RYJhJhV',
	    'Po_IlI RRKYK RRQYQ RRWYW R][k[ RRERY RREYEYW RlWk[ R^NiM]Y RaC\\M',
	    'Po]FjF RRKYK R[MlM RRQYQ RRVYV Rf[l[ RRERY RREYEYV RfMf[ R^WZ[ RlWk[ R`M_W',
	    'PoRKYK R_NhN RRQYQ R]ShS RRWYW Re[j[ RRERY RREYEYV RaC]L R_IlHlSj[',
	    'PoSI]I R`LlL R`RlR R`YlY RXCX[ R`E`[ R`ElEl[ RXM]Q RXISU',
	    'PoRKYK RRQYQ R]TjT RRWYW Rd[l[ RRERY RREYEYW R]K]V RdCd[ RjKjV RlXk[ RjF[H',
	    'PoRKYK R_LlL RRQYQ RRWYW RRERY RREYEYV RgLg[ RjD^F_P]X',
	    'PoRKYK R]LiL RRQYQ RRVYV Ra[f[ RRERY RREYEYV R_EgDjK R^F[L R`L^SiRg[',
	    'PoRKYK RRQYQ RRWYW Rb[g[ RRERY RREYEYW R_V[[ R`EgEkN R^G\\M RaN_W R]NiNiUh[',
	    'PoZHlH RRKXK R[NkN RRQXQ RZTlT RRWXW RRERY RREXEXV RcCc[',
	    'PoUJlJ RZRjR RZVjV RZZjZ RZNZ[ RUF[FaEaM RZNjNj[ RUEUQTY RiD_E',
	    'Po[IlI RRKYK RRQYQ RRWYW RRERY RREYEYV RbDcH R_MbUfXl[ RgMdT`XY[ R^IaS RhIeT',
	    'PoRK[K RaKlK RRQ[Q R`SlS RRW[W Rg[l[ RRERY RRE[E[W RaElEl[ R_W[[ RaE`P_W',
	    'Po[K_K RXRhR RXVhV RXZhZ RXNX[ R_C_K RUOhOh[ RYETJ RfI`L\\MRO RfElI',
	    'PoRKYK R]NkN RRQYQ RRVYV RRERY RREYEYV R]F][ RdY\\[ RlWk[iZ RfEeNgX RjD]F',
	    'Po]FjF RRKYK R[NlN RRQYQ RRWYW RcYjY R_YfY RRERY RREYEYV RhTk[ R^U\\Y RbN^W RcY\\Y',
	    'Po[ElE RRKYK RRQYQ RRWYW Rd[j[ RRERY RREYEYV RcEcR RcKlKlW R]J]SkQj[',
	    'Po\\FcF RRKYK RRPYP RRVYV RRERY RREYEYV R_LcUhYl[ R\\LjLgSbX][ R\\F\\Q[X RjD_F',
	    'Po[HlH RRKYK RRQYQ RRVYV Rc[h[ RRERY RREYEYV RcCcH R^WZ[ RaH`OjNiUh[ R`J_V',
	    'PoRKYK RRQYQ R_RcR RRWYW RRERY RREYEYW RcCcR RhGlO R^H[P RiPgTdX`ZZ[',
	    'PoRKYK RRQYQ RRVYV Re[l[ RRERY RREYEYV R\\I\\M ReNeZ R\\IlIlM RlWl[ RcCcP`U\\YY[ RcJbT',
	    'PoVJlJ RZRlR RZVlV RZZlZ RZMZ[ RaEaJ RVElElJ RZMlMl[ RVEVQTX',
	    'PoRKYK RRQYQ RRWYW Rb[k[ RRERY RREYEYW RcFc[ RlXk[ RiD[G RjJ[M RlQ[T',
	    'PoUHjH RRLlL RYSiS RYWiW RYZiZ RYNY[ RiC^EUE R]EZMST RYPiPi[',
	    'PoYHhH RYLhL RYPhP RTSlS RTFTT RYDYP R_S_[ RYDhDhP RiXk[ ReVhX RXVVYRZ',
	    'Po]EkE RRKYK RRQYQ RRWYW R][l[ RRERZ RREYEYW R]E][ R`JdN RdS_X RfPhSjW RiHdS',
	    'Po\\EjE RRKYK R[LkL RRQYQ RRWYW R_[c[ RRERY RREYEYW RcLc[ RhPlX R^QZY',
	    'PoRKYK RRQYQ R[QlQ RRWYW RRERY RREYEYV R]H]Q R]HjHjQ ReTl[ RbU]Z RdCcPbV',
	    'Po[FlF RRKYK RcOkO RRQYQ RRWYW RY[l[ RRERZ RREYEYW R\\M\\[ RcFc[',
	    'PoRKWK RRQWQ RRWWW Rg[j[ RRERY RREWEWV R\\SYW R`RbY ReRa[ R^DZN RaK]R RcIlIlTjZ RfCfMdW R`MYN RaVYW',
	    'PoRKYK R]MlM RRQYQ R]SlS RRWYW RRERY RREYEYV R]G]U RdCd[ R]GlGlU',
	    'Po\\IkI RRKXK RRQXQ RRWXW RRERY RREXEXV R\\I\\Y RgDjG RgS`[ RjLfV RaV[Y RlWk[iZ RdCcIeV',
	    'PoTJjJ RZ[_[ RTETJ R[E[J R_K_[ RbEbJ RTEjEjJ RUNYR RePcS RiMeQ R_OeVlX R\\URX',
	    'PoRKYK RRQYQ RRWYW RRERY RREYEYW RcCkK RbD[L RdKaM\\O RfOdR]T RjSfWaY[[',
	    'PoRKYK R]NjN RRQYQ RRVYV Rf[l[ RRERY RREYEYV R]E]N RfNf[ R]EjEjN RlWl[ R_W[[ R`N_X',
	    'PoRKYK RRQYQ R^TfT RRWYW Rd[j[ RRERY RREYEYV R^M^V R^MfMfT R`C\\L R^HlHlSj[',
	    'PoSGkG RWRfR RWVfV RWZfZ RXMX[ RVNgMg[ RUIXL RhIfL RbIgNlP R^C\\IVNRP',
	    'PoRKYK RaMkM R^MhM R\\MfM RRQYQ RRVYV R]ZjZ RRERY RREYEYV R^Q^[ R]QjQj[ RfHkL RbC^M',
	    'PoVGjG RVJiJ RRMlM RVShS RVWhW RVZhZ RVPV[ R`C`M RVPhPh[ RXDSI',
	    'Po\\HjH RRKYK R[OlO RRQYQ RRWYW RRERY RREYEYV RcCc[ RfSlY RcMaRYY RcMdPfS',
	    'Po[IlI RRKYK R\\OkO RRQYQ RRWYW RRERY RREYEYV RcCc[ RfSlY RaSZY RcMeS RcMaS',
	    'PoRKYK R\\NlN RRQYQ RRWYW R`[d[ RRERY RREYEYV RdNd[ RcDdF R]H\\L R\\HlHlL',
	    'PoYEaE RVF^F RVHjH RYNiN RYQiQ RYSiS RTVlV RTKTV RWCWH RYKYS RYKiKiS RcXl[ R[XR[ RfD_E',
	    'PoSFkF RWLhL RWPhP RWShS RRVlV RWIWS R_C_I RWIhIhS RcXl[ R[XR[',
	    'PoRKYK R]KjK R]QkQ RRQYQ RRWYW RRERY RREYEYW R]E]Z R]EjEjK RcZZ[ RdKgYk[mW',
	    'PoRKYK R]NlN RRQYQ RRWYW RRERY RREYEYW R]G][ RcWd[ RaZ[Z RlWk[iZ RiD\\F RdEdKfX',
	    'PofJjJ RbNlN RWShS RWWhW RWZhZ RWPW[ RbEbM RWPhPh[ RaEjEjI RlKkN RUHYJ RYCSJ RWF^F]IYMSO',
	    'Po]IjI RRKYK RZPlP RRQYQ RRVYV RRERY RREYEYV ReUj[ RbCbPfX RbP`V]YX[ R^E\\M RbLaU',
	    'Po]KkK RRKYK RRQYQ RRVYV Ra[k[ RRERY RREYEYV RbMb[ R]EkEkK RlWk[ RkOaS R]E]Q[Y',
	    'PoYG_G RbMlM RWShS RWVhV RWZhZ RTFTM RWPW[ RYCYM RbCbM RWPhPh[ RlJkM RkFaH R^LSM',
	    'PoRKXK RRQXQ RRWXW Rf[l[ RRERY RREXEXW R\\J\\[ R`D`Y RfDfZ RkIfO R`McM`M RcYYZ RlVl[',
	    'PoRKYK RfNjN RRQYQ RRWYW R^ZkZ RRERY RREYEYV R^Q^[ R^QkQk[ RbI]N RdE`L R]ElEkN',
	    'PoRKYK RRQYQ RRWYW Ra[l[ RRERY RREYEYW RdFdL R]U[[ R\\LjLhP_Yg[ RjD\\F',
	    'Po[HlH RRKYK RRQYQ RRWYW RZZkZ RRERY RREYEYV R]NbS RfTkZ RePbS RaU]Z RgKeP RcCcGaI_N',
	    'Po[ElE RRKYK RRQYQ RRVYV Rf[k[ RRERY RREYEYV R\\K\\[ R\\KkKk[ RdEcOiT RbQ^U RcHbQ',
	    'PoRKYK R\\MkM RRQYQ R\\SlS RRWYW RRERY RREYEYV RdCd[ R^F`K RjEgK',
	    'Po[JlJ RRKYK RRQYQ RRWYW RdZlZ RRERY RREYEYV ReJeZ RlWlZ R]VY[ RbX^Y RiPaX ReEiF RjMgS R_Q\\Y R`C`K^U',
	    'Po]HjH RRKYK R\\NlN RRQYQ R\\TjT RRWYW RZ[l[ RRERZ RREYEYW RcDcN RcPc[',
	    'Po_IiI RRKYK RRQYQ RaUhU RRVYV Rh[l[ RRERY RREYEYV R]E][ RaMaV RaMhMhU R]ElEl[',
	    'PoRKYK R[NlN RRQYQ RRVYV RRERY RREYEYV RcCc[ RfSlX R_SZX R\\E_K RdNgT RbN_U RcL`S RjEgK',
	    'Po\\EkE RRKYK R]MjM RRQYQ R\\TkT RRWYW RZ[l[ RRERZ RREYEYW RcOc[ RfIjL RdEaH\\M',
	    'Po[FlF RRKYK R]NjN RRQYQ RRVYV RRERY RREYEYV R\\JjJjN RcCcReVhYl[ RbU^YZ[ R]N\\RkRjWhV RcMbU',
	    'Po[ElE R^KiK RRKXK R^PiP RRQXQ RRVXV RZWlW RRERY RREXEXV R^E^W RiEi[',
	    'PoRKYK RRQYQ RRVYV R`ZlZ RRERY RREYEYV R`Q`[ R_RlRl[ RbCaG\\K RaLeM RaGjGhKbP[S',
	    'PoRKYK RRQYQ R^QfQ R^UfU RRVYV Rd[j[ RRERY RREYEYV R^L^W R^LfLfU R_C[LYM R^HlHlSj[',
	    'PoRKYK RbQlQ RRQYQ RRVYV RRERY RREYEYV R^HbJ RcC^H[J ReL[P R`GiGdMaR[T R_SbTgW^Z RkReX[[',
	    'Po\\EkE R_IjI RRKYK R`OiO RRQYQ R_UkU RRWYW R\\YlY RRERY RREYEYV R\\E\\[ ReIeU',
	    'PoXHfH RRLkL RXSfS RXWfW RXZfZ RXOX[ RWPgOg[ ReDjI RYDTI RbHcLhPlR R_C[LVQRR',
	    'Po[JkJ RRKYK R]PkP RRQYQ R[UlU RRWYW RRERY RREYEYV RcKc[ RgFkK R`G]J RaC_H R^L\\R',
	    'Po`EgE RRKYK R^LiL RRQYQ R[RlR RRWYW RRERY RREYEYW RgFlJ R_G[K RcLcReWhYl[ RcRbV_YZ[ RcNbV',
	    'PoRKXK RRQXQ RRVXV Re[l[ RRERY RREXEXV RfCfZ RlWl[ R\\H]L RgPkT RkHgL R`Q]RZS R`C`Q_VZ[',
	    'Po\\IlI RRKYK R]OkO RRQYQ R[UlU RRWYW RRERY RREYEYW RdId[ R^D`H RiCgH',
	    'Po^JjJ RRKYK R^OjO RRQYQ RRWYW RRERY RREYEYW R^E^Z R^EjEjO RkReU ReYZZ RcPfVk[',
	    'PoRKYK RRQYQ RRVYV RRERY RREYEYV R`K`Z RjOfRjZ ReMhX ReJgV RjI`K RgY^Z RjD\\F\\Q[Y',
	    'PoTJjJ RTDTJ R[D[J R`O`[ RcDcJ RTDjDjJ RXR\\Z RhSkZ RVTS[ RcL_PZR RWNZX RgNjY RWLUX RgMdX',
	    'PoRLYL R[NaN RRQYQ RRWYW R_[d[ RRFRZ RRFYFYW R]IdId[ RhTlX R_T[X RkLfQ RdKiV R`CgF R`O]W',
	    'PoTFkF RUJiJ RSMlM RYSiS RYWiW RYZiZ RYOY[ R_F_M RYDZF RfDcF RYNWRRW RYQiQi[',
	    'PoRKXK RZOlO RRQXQ R\\TiT RRVXV R^[b[ RRERY RREXEXV RbJb[ R[KjKjU R_FgFbK R_FZK',
	    'PoRMlM RZTiT RZWiW RZZiZ RZPZ[ RZPiPi[ RlClO R_GiK R_CiF R_G[ITK R^DTG RWMWPUX',
	    'Po]IiI RRKXK RRQXQ RRVXV RRERY RREXEXV RgKlO RfEkJ RaO[U RcC\\I R`K\\P R_RdXk[ R_RiRdW_YZ[',
	    'PoRKYK R]PkP RRQYQ R]UkU RRVYV Rf[k[ RRERY RREYEYV R]K][ RdCdK R]KkKk[ R]E_I RlEhH',
	    'Po]IjI RRKYK R]MjM R\\PkP RRPYP R[UlU RRVYV RRERY RREYEYV R]D]M RdPd[ R]DjDjM',
	    'PoRKXK R]LjL RRQXQ R[QlQ RRWXW Rf[l[ RRERY RREXEXV R[G[J RfQf[ R[GkGkJ RlWl[ R_W\\ZY[ RcCcG R`Q^X',
	    'PoRKYK R]MkM RRQYQ RRVYV RgXkX RRERY RREYEYV RcHc[ R[HkHkM R^C`G RhCfH RcQ]WXY R\\M[SlRkX',
	    'PoRKXK R]KjK R[PkP RRQXQ R\\UkU RRWXW RZ[l[ RRERZ RREXEXW R]E]K RcPc[ R]EjEjK',
	    'PoRKYK RRQYQ R\\TkT RRWYW R[[l[ RRERZ RREYEYW RcCc[ R^KbO RjLkP RhJjN RhGfP R^F]P',
	    'Po_JkJ RRKYK RRQYQ R^RlR R^VlV RRVYV Rh[l[ RRERY RREYEYV R^M^[ R_D_J R_DkDkJ R^MlMl[',
	    'PoRKXK R[LlL RRQXQ RRVXV R\\[_[ RRERY RREXEXV R_E_[ RhEkH RgUa[ RjNfW RcQ[S RcD[F RfCfNhY RlWk[i[',
	    'PoRKXK RRQXQ R[RkR RRWXW Rd[l[ RRERY RREXEXV R\\I\\R RdRdZ R[KkKkR RlWl[ R_FYL R_C]H RaT]YX[ R^FfFdIcKaU',
	    'Po]IjI RRJYJ R]NjN RRPYP R]RjR RRVYV Re[l[ RRERY RREYEYV R]D]R RfRf[ R]DjDjR RlWk[ R`S_X]YY[',
	    'PoRKXK RZLlL RRQXQ RRWXW RgYjY RRERY RREXEXV R^P^Y RdNd[ R\\RjQjX R`NZS RaI^P R\\DkI RiCbG[I',
	    'PoRKYK R^KjK RRQYQ RRWYW R\\ZlZ RRERY RREYEYV R\\D\\[ RdGdW R\\DlDl[ RdJiT RdJ`T',
	    'Po[GjG RRKXK RZPkP RRQXQ RRWXW RRERY RREXEXV RbCb[ RbPjX RcPZX R\\I^M RhIfN',
	    'PoRKXK R^MgM RRRXR RRXXX RZ[l[ RRERZ RREXEXX RcEkM RbCaGZN RjPe[ R\\Q^W RbPcW',
	    'PoRKYK RRQYQ RRVYV RRERY RREYEYV RkQgU RjFgI RbJkN RdUgYl[ R^F\\J R]Q[V RbI_M[N RcQbV_YY[ RcDaK RcOdV',
	    'PoRKXK R\\NjN RRQXQ RZUlU RRVXV RRERY RREXEXV R\\G\\T RaCaT ReCeT R\\GjGjT RgVk[ R`V[[',
	    'PoRKXK R^KhK RRPXP R[TkT RRVXV Rh[j[ RRERY RREXEXV R[O[[ R`O`[ ReOe[ R[OkOk[ RcC[K RcDhIlK',
	    'PoRKXK RRQXQ RgTjT RRVXV Re[l[ RRERY RREXEXV R[F[J ReKe[ RdKjKjT R[FkFkJ R]Q`T RbDeF R_VZ[ RlXl[ R^IZR RaP]Y R]LbL_W',
	    'Po\\FjF RZKlK RRKXK RRQXQ RRVXV RRERY RREXEXV RcCcK ReKePkOkP R`L\\P RaP[W R_ShScXl[ R_SdW`YZ[',
	    'Po^IkI RRKYK R^MkM RRQYQ RRVYV Rf[j[ RRERY RREYEYV R^D^M R^DkDkM R_P[T RbS\\Y RfVa[ RgQdY R`M^RlQk[',
	    'PoUIhI RSLkL RWSgS RUDUI RWNWS R[U[Z R[D[I RbEbI RWNgNgS RUDhDiI R_ScXj[ RjTcW R_SZURW RbYV[',
	    'PoRKXK R\\LkL RRQXQ R\\RlR RRVXV RRERY RREYEYV R\\GkGkL RfVjZ RhOkP RbDeF RdRgY ReRcW`Y\\[ RdOeV R\\G\\Q[X',
	    'Po\\ElE R_JjJ RRKYK R^OlO RRQYQ R_TkT RRWYW R\\ZlZ RRERY RREYEYV RdFdO RdPdZ R\\E\\Q[Y',
	    'Po\\EkE R]IjI RRKYK R[LlL RRQYQ R]UjU RRVYV Rf[j[ RRERY RREYEYV R]O][ RcCcL RcOcU R]OjOj[',
	    'PoRKXK RZPlP RRQXQ R[UiU RRVXV R][b[ RRERY RREXEXV RbLb[ R\\LiLiV RhFkI RbGbJ R]EZK RhD\\E',
	    'PoRH_H RgOkO RWShS RWWhW RWZhZ RWPW[ RYDYN RWPhPi[ RYHRN RYJ^L RcC`J RgFdM RbGlFkN R]CRE',
	    'PoZGkG RRKXK RRQXQ RRVXV RRERY RREXEXV RbCb[ R]L`O RbPjX RgMkP RbPZY RhJeP R^J[P',
	    'Po[GkG RRKYK RRQYQ RZTlT RRVYV RRERY RREYEYV RcCcG RcQc[ R^LaO RgLkP R_I\\O RgIeO',
	    'Po]IiI RRKXK RZNlN RRQXQ RRWXW RcWgW R^[j[ RRERY RREXEXW R_R_[ RfNlS RgDlH R`NZS RjXj[ R^E[I ReIhQ RbH^Q R^RgRgW RcC`O',
	    'PoZIkI RRKXK RZOlO RRQXQ RZUkU RRWXW RZ[l[ RRERZ RREXEXW R]I]U R[EcEc[ RhIhU RiDbE',
	    'Po^IkI RRKYK R^NkN RRQYQ R^TkT RRWYW R^ZlZ RRERY RREYEYW R_G_[ RfGfZ R`C[N RfDeK',
	    'PoYE^E RRI_I RWTgT RWWgW RWZgZ RWPW[ RYCYO RWPgPg[ R\\J^M RUKRN RbEcIgMlO R`EjEiHeL_N',
	    'Po\\IjI RRKYK R\\NjN RRPYP RZQlQ RRVYV RRERY RREYEYV R\\D\\N RcDcN RgQg[ R\\DjDjN R_W[[ R`Q^Y',
	    'Po]KjK RRKYK R]OjO RRQYQ R[UlU RRWYW RRERY RREYEYV R]F]O RfQf[ R]FjFjO RbCcFcO_S',
	    'Po\\GjG RRKXK RZLlL RRQXQ R[UjU RRWXW RX[l[ RRERZ RREXEXW RcCcL RcQc[ RfLfQlPlQ R`L^Q[R',
	    'Po]JlJ RRKYK RRQYQ R]VjV RRWYW R]ZjZ RRERY RREYEYW R]F]O R]R][ R]RjRj[ RlMlP RdIgOkP RdEfM RdN[O RiD]F',
	    'PoRKYK R]KaK ReKjK RRQYQ R]QjQ RRWYW Rf[l[ RRERY RREYEYV R]E]Q RfQf[ ReEjEjQ RlXl[ R`R^W\\ZX[ RcD\\E',
	    'Po]IiI RRKYK R[MlM RRQYQ RRVYV R`[c[ RRERY RREYEYV RcMc[ R]EiEiM R]O`S RjOhRdShWlX RcT[X',
	    'PoTIjI RULiL RROlO RSTkT RTWjW RTDTI R[D[I R_J_O R_T_[ RcDcI RTDjDjI RYQ[R ReQcR',
	    'Po[FlF RRKXK RZMlM RRQXQ R\\QiQ RcUjU RRWXW Ra[l[ RRERY RREXEXV RcCcZ R\\IiIiQ R\\V`Z R]SZZ',
	    'PoRKXK RRQXQ R[QkQ RRWXW RRERY RREXEXV RcMc[ RdRlZ RaSZZ RbGcK RhFkL R]E[L RhD_E',
	    'Po_JiJ RRKYK R_NiN RRQYQ RRVYV R`WhW Rg[l[ RRERY RREXEXV R`R`X RdGdN R`RhRhW R\\ElEl[ R\\E\\TX[ R\\N[X',
	    'Po]JgJ RRKWK RRQWQ R[QaQ R[UaU RRWWW R^[a[ Rf[j[ RRERY RREWEWV R[M[[ R[MaMa[ ReNeW RjMj[ RcDeGlJ RcC_HYJ',
	    'PoaGlG RSH]H RSK^K RUTgT RUWgW RUZgZ RSDSP RUQU[ RSD]D]H R^N^P RUQgQg[ RYM\\P RbFcJfNlP RbC`J RiFgKdN_O RXHZN RXORP',
	    'Po[GlG RRKYK RZNlN RRQYQ RRWYW R_ZjZ RRERY RREYEYW R_Q_[ R`C`J RgCgJ R^SjSj[ R_QZV RbJ^S',
	    'PoRKXK R[KbK RdKkK R[PaP RRQXQ R[UbU RRWXW RRERY RREXEXV R[E[[ R[EbEbK RdEkEkK RfVl[ RiTcZ RdPhX RcOkOgW',
	    'Po]IjI RRKYK R]MjM RRQYQ R]RkR R]VkV RRWYW R[[l[ RRERZ RREYEYW R]E]M RdOd[ R]EjEjM R^O\\T',
	    'PoZGlG RRKXK R]MiM RRQXQ R[QkQ R\\ViV RRWXW RY[l[ RRERZ RREXEXW RcJcQ RcSc[ ReGjM RaGZN RbC_J',
	    'Po\\IjI RRKXK R]PjP RRPXP R]TjT RRVXV R[WlW RRERY RREXEXV R[E[H R]L]T RcGc[ R]LjLjT R[DkEkH',
	    'Po[GcG RRKXK RZLlL RRQXQ RRVXV R[WbW RRERY RREXEXV R[O[Y R_C_L R[ObObV RgEkH RhTa[ RjNfV RlWk[i[ RfCeMgY',
	    'Po[FkF R\\JjJ RRKXK RZNlN RRQXQ R^UgU RRWXW R^ZgZ RRERY RREXEXV R^P^[ R]QhQh[ RfNlT R`NZT RbH^P RcC`N',
	    'Po_JlJ RRKXK RaMjM RRQXQ R`QkQ R_UlU RRVXV Rb[l[ RRERY RREXEXV RfCfY RaFjFjM R\\WX[ R^R[Y RYF^EYN^N\\X RZR\\W`Zg[',
	    'Po]KlK RRKYK RRQYQ R`RlR R`VlV RRWYW R`ZlZ RRERY RREYEYV R`N`[ RdEdJ R`NlNl[ R]ElElJ R]E]Q[Y',
	    'Po\\GgG RRKYK R[LlL RRQYQ R^VjV RRVYV R^ZjZ RRERY RREYEYV R_Q_[ RbCbL R]RjRj[ RfNiO RjGbOZR',
	    'Po^JlJ RRKWK R`OkO RRQWQ R^TlT RRVWV RRERY RREWEWV R\\H\\[ RaEhEhJ RfWj[ RdV`Z ReNeThY R^CYN RaK_R RePdW',
	    'Po_KiK RRKYK RRQYQ R^SjS RRWYW R^WjW R[[l[ RRERZ RREYEYW R\\G\\J R^N^W R^NjNjW R\\GlGlJ RdCdG',
	    'Po]HiH RRKXK R]LiL R[PlP RRQXQ RcUjU RRVXV RbZlZ RRERY RREXEXV R]E]L RdPdZ R]EiEiL R^VaZ R]SZZ',
	    'PoRKXK R]NhN RRPXP RZSkS RRVXV RRERY RREXEXV RfIlN RhEfH R_IZN RcReW RdDhL R[H_J RcNcUhYl[ R\\EaE^K RlHgJ RbU_YZ[',
	    'PoTIjI RULiL RRPlP RSTkT RTWjW R[E[J R_I_O R_T_[ RbEbJ RYQ[R ReQcR R^C^F RTEjEjJ RTETJ',
	    'Po_EhE RXTfT RXWfW RXZfZ RSHTK RXQX[ RVRgQf[ RTHjHjK RXJeKjN R`MjR RYLTN R]NRS R_C_H R_M]M',
	    'PobGlG RVShS RVWhW RVZhZ RVPV[ RWOXPhPh[ RRI_I\\K RbGfLlN RWF\\H RcCaI RiFhIeL_O RXJRM RSE^DZHZNWN',
	    'Po\\HjH RRKYK R\\LjL R\\PjP RRQYQ RZTlT RRVYV RRERY RREYEYV R\\E\\P R\\EjEjP RfQjR RdVjX RbRaW_YY[',
	    'PoTI_I RaJlJ RVR^R RVV^V RVZ^Z RTEUQV[ RZEZN RVN^N^[ RfVl[ RgDjG RhUb[ RdCcOgX RcNjOfX RTPR[ RcLaX R^DTE',
	    'Po\\FbF R[IlI RRKXK RRQXQ R_RkR R_VkV RRVXV R_ZkZ RRERY RREXEXV R_N_[ RdEdN R_NkNk[ R[F[RZZ RjD`E',
	    'PoZGlG RRKXK RRQXQ R\\SjS RRVXV R\\ZjZ RRERY RREXEXV R\\M\\[ R_D_K RcMcY RgDgK R\\MjMj[',
	    'PoRKXK RZKaK RRQXQ RRVXV RRERY RREXEXV R^D^[ R^MaQ RhTk[ RfRb[ RkIjP R^JZV RgCgPdY RdIcO RaDZE RgJiW R^H\\R RgKeW',
	    'Po\\IjI RRKXK RZLlL RRQXQ RRWXW RRERY RREXEXV R\\E\\I R\\O\\W RcCcL R\\OjOjW R\\EjEjI ReXk[ RbX[[ RcRbX',
	    'PoRKXK R_MlM RRQXQ RRWXW Ra[l[ RRERY RREXEXV RfCfX RZF]I RgQjT ReNiR RcR_V RZM]M]XZZ RaEbJ R]Xc[ RjEiK ReLbT',
	    'PoRKXK R`LiL R]MfM RRQXQ RRWXW R^[a[ Rg[k[ RRERY RREXEXV RZPaPb[ RdPkPk[ R\\R^T RdRgS RfIjM R`D[I RbG\\M ReDhGlI RaUZX RkUcX',
	    'PoRF_F RSI^I RiLlL RUO\\O RTT]T RTW]W RTZ]Z RTQT[ RXCXI RTQ]Q][ RSLRM^L^O RcEcK RcEiEiL RdTjZ RcQfW RaPjPhUcY`[',
	    'Po\\GlG RRKYK R^NlN RRQYQ R_QjQ RRVYV R_ZjZ RRERY RREYEYV R_T_[ RdHdT R_JjJjQ R_TjTj[ RcCfF R\\G\\QZY',
	    'Po]IkI RRKXK R_NjN RRQXQ R_QjQ R_TjT RRWXW R[WlW RRERY RREXEXV R[L[W R]C]H R_K_T R_KjKjT RfXl[ R_XY[ RdE]F RiD_F',
	    'Po\\GjG RRKXK RZKlK R\\PjP RRQXQ RRWXW RY[l[ RRERZ RREXEXW R\\T\\[ R`T`[ RcCcK ReTe[ R\\TiTi[ RgMkQ R`K^P',
	    'Po\\J`J RfJjJ RRKXK R\\NjN RRQXQ RRVXV RRERY RREXEXV R\\F\\N RcCcR ReFjFjN RaE\\F R^SaWeYl[ R[SjSfWaYY[',
	    'Po\\JeJ RRKYK R]MjM RRQYQ R[QlQ RRWYW R]ZjZ RRERY RREYEYV R[F[I R]T][ RcCcF RcHcS R]TjTj[ R[FlFkI RiIcJ',
	    'PoSHjH RUKiK RRNkN RVThT RSDSH RVQVT RYVX[ R[D[H R_I_N RcDcH RVPhQhT RSDjDjH R_TbWjZ RiUdX R`TSX RaZX[',
	    'PoWGhG RWJhJ RWMhM RRS]S RaSlS RRV]V RaVlV RRZ]Z RaZlZ RRPR[ RWDWM RRP]P][ RaPa[ RWDhDhM RaPlPl[',
	    'PoRKYK R]MiM RRQYQ R]QiQ R[UlU RRVYV RRERY RREYEYV R[E[H R]I]P R]IiIiP R[EkEkH RhWk[ R^XY[ RcQcU',
	    'PoYFlF RZJkJ RRKWK RRQWQ RRWWW Rf[j[ RRERY RREWEWV R[N[[ R_QaU R_C_H RfCfH R[NjNj[ ReJeRhU R`L^V ReMdV',
	    'PoZHaH RRKXK R[K`K R[O`O RRQXQ RRVXV R[Y`Y RRERY RREWEWV R[S[[ R[S`S`Y RhEhX RlDl[ R]C^F RdDdSb[',
	    'Po\\EkE R]HjH R[JlJ RRKYK R]PjP RRQYQ R]SjS RRVYV R]WjW RRERY RREYEYV R]M]W RcCcJ R]MjMjW RgXl[ RaX[[',
	    'Po\\FkF R[JlJ RRKYK R]PjP RRQYQ R]SjS RRWYW R[WlW RRERY RREYEYV R]M]S RcCdF RcSc[ R]MjMjS R_F`J RhFfJ',
	    'PoRD^D RUH]H RTJ^J RRO^O RUTgT RUWgW RUZgZ RRDRO RUQU[ RUQgQg[ RYJUM RbJcLjP RXK]M RbD`H RYGXL R`JjJfN_O RbDhDhHlGlH',
	    'Po]IiI RRKYK R]MiM R]PlP RRQYQ RRVYV Re[j[ RRERY RREYEYV R_V`Y R]FiFiL RcUdY RfUhW RcDaF R\\VZZ R]F]RkSj[',
	    'Po[EkE RRKXK R`NhN RRQXQ R^XcX RfXkX RRXXX R[[l[ RRERZ RREXEXX R[E[[ R^Q^W R`H`N R^QcQcW RfQfW R`HhHhN RfQkQkW',
	    'Po[FlF RRKYK R]NjN RRPYP R]QjQ R[UlU RRVYV RRERY RREYEYV R]J]Q R`C`H RgCgH R]JjJjQ RdVgYl[ RbV`Y[[ RcQbX',
	    'Po]JiJ RRKXK RRQXQ R\\RjR R\\VjV RRWXW RRERY RREXEXW R[G[J R\\N\\V RcCcG RcJcV R\\NjNjV R[FkGkJ RfXl[ R`XY[',
	    'PoRKXK RRQXQ ReSkS RRVXV RRERY RREXEXV R]L][ RbWeZ RbGeJ RhHkK RaTcY R]C[H R^IZQ RiEeL RcD`L RaP_Z RfNeZk[',
	    'Po[HlH RRKXK R\\KjK RRQXQ R[QjQ RZTlT RRVXV RRERY RREXEXV R[N[Q R\\E\\K RcCcQ R\\EjEjK R[NjNjQ RaQ_U]WkZ RgUdYZ[',
	    'Po[FlF RRKXK R]OjO RRQXQ RRVXV Rg[k[ RRERY RREXEXV R[R[[ R]H]O RjHjO R[RkRk[ RfThW RcCdF R`IfK RcO_W RgHdK_M RgV^X',
	    'PoZFlF R^JhJ RRKXK RRQXQ RRVXV Rh[k[ RRERY RREXEXV R[M[[ R^C^J RcJc[ RhChJ R[MkMk[ RgQeW R_Q]W RePgPiW R]P_P`W R_P]P RgPeP',
	    'PoZDlD RRKXK R\\MkM R\\QjQ RRQXQ RZTlT RRWXW R_[c[ RRERY RREXEXV R\\H\\M RaDaM RcTc[ RfDfM R\\HkHkM RhWlZ R^VZZ',
	    'PoRKXK RRQXQ R^RiR R\\VjV RRWXW R[[l[ RRERZ RREXEXW R[I[M R^M^R RcCcI RcRc[ R^MiMiR R[IkIkM R^D_H RiDhG',
	    'PoYH`H RcIkI RRKXK RcNkN RYOaO RRQXQ RRVXV Rh[l[ RRERY RREXEXV RcDcRjRi[ RcDkDkR RlXl[ R]D^S`W RdW`[ RdScY R]M\\V',
	    'PoRElE RULjL RWTgT RWWgW RWZgZ RSNSQ RUHUL RWQW[ RZCZG R[H[L RbHbL RdCdG RWQgQg[ RUHjHjL RSNkNkQ',
	    'Po\\JjJ RRKWK RRQWQ R[QkQ RRWWW R_[g[ RRERY RREWEWW R[M[Q R\\F\\J R`V`[ RcCcT RgYg[ R\\FjFjJ R[MkMkQ RhVlZ RaTeV R\\VZZ',
	    'Po\\HbH RfHlH RRKXK R\\LbL RfLlL R`OhO RRQXQ R`ShS RRVXV R_WiW Ri[l[ RRERY RREXEXV R\\D\\[ R\\DbDbL RdOdW RfDfL RfDlDl[',
	    'PobGlG RWTgT RWWgW RWZgZ RTGTO RVJVM RWQW[ RYCYO RWQgQg[ R\\D_G RUDRG R\\J\\M RbGcJgMlO RcCaI RiHeM`O RTG^G^O\\O',
	    'Po[ElE R\\IkI RRKYK R]OjO RRQYQ R[SlS RRVYV R]ZjZ RRERY RREYEYV R]K]O R]V][ RcCcI R]KjKjO R]VjVj[ R_P_S RgPgS',
	    'Po]HkH RRKXK R]LjL R]OjO RRQXQ R]TlT RRWXW RRFRY RRFXFXW R^G^S RdFdS R^HZL ReVf[ RiVk[ R_C\\J ReDdI R]V[[ RaWa[',
	    'Po[IlI RRKXK R\\NkN RRQXQ R\\QjQ R[UlU RRVXV RRERY RREXEXV RaCaI ReCeI R\\D]G RjDiG R_J_M RdWl[ RgJgN RcNcU`Y[[',
	    'Po\\IkI RRKXK RRQXQ R_RkR RRWXW R_XiX Ri[l[ RRERY RREXEXV R^T^[ R_N_R R^LeKeX R_NkNkR R\\EkEkI R^TlTl[ R\\E\\PZX RjKcK',
	    'PoRKXK R^LfL RRQXQ R\\TiT RRWXW RY[l[ RRERZ RREXEXW R\\O\\T R\\OiOiT RfHlN RiDfH RjHhJ R[G^IZN R_V_Z RdDgJ RfVfZ R[EaE^K',
	    'PoZDlD RRKXK R\\LkL RRQXQ R\\QjQ R\\TjT RRVXV RZWlW RRERY RREXEXV R\\G\\K R\\N\\T RaDaK RcTc[ RfDfK R\\NjNjT R\\GkGkK',
	    'PoRKXK RcPlP RRQXQ RaVlV RRVXV RRERY RREXEXV R[K[N RdPdV RiMi[ R[KkKkN RhGgK R_UZ[ R]F]I RZEbFbI R[S_U R]MZT R]PaP^X RjCaD',
	    'PoZGlG RRKXK R]PhP RRQXQ R]ThT RRVXV R_[b[ RRERY RREXEXV R]K]T RcTc[ R]MiMhT RgVkZ R[I]L ReHjN RjIhK R]VYZ RcC_I[M',
	    'PoYGcG RRKWK RYLbL RRQWQ R[RaR R[VaV RRVWV R^[a[ RRERY RREWEWV R[O[[ R[OaOa[ R`IcL RgWc[ RfQjZ RiQeY R^E[L RfMiX ReIgV ReIkIhV RfDcP',
	    'Po\\HbH RfHlH RRKXK R\\LbL RfLlL RRQXQ RaRgR RaVgV RRVXV ReYgY Ri[l[ RRERY RREXEXV R\\D\\[ R\\DbDbL RfDfL RaOgOgY RfDlDl[ RaOaT`Y',
	    'PoZDbD RRKWK R[NaN RRQWQ R[RaR RRVWV RRERY RREWEWV R[I[X R^D^I RYIbIa[ RhWl[ RhVc[ RfQjZ RiPeY ReJgV ReJkIhV ReCcO RaWXX',
	    'PoZFlF RRKXK RZNlN RRQXQ R[SkS RRVXV RZWlW RRERY RREXEXV R^J^S R`C`H RcJc[ RgCgH R[JjJiS',
	    'PoRKYK R`NdN RRQYQ RfSlS RRWYW R`YhY RRERY RREYEYW R\\P\\[ R`U`Z RdIdN RePfS R`UhUhY R[IlIiK R]DjDdG RcJ[M RaP`S]T R`FiI R\\PlPl[i[',
	    'Po[FkF RZJlJ RRKXK R\\QjQ RRQXQ R\\TjT RRWXW R[WkW RY[l[ RRERY RREXEXW R\\M\\T RcCcF RcMc[ R\\MjMjT RgGfI R_G_J',
	    'Po\\GlG RRKWK RYMlM RRQWQ RYTlT RRVWV RRERY RREWEXV R]F]T R_W`[ R`G`T ReGeT RiGiT RhWlZ R[XW[ RdWe[ R]CZJ',
	    'PoZHlH RRKXK RRQXQ RcQlQ RRWXW RaWlW RRERY RREXEXV RcCcN RcQcV RhNh[ RZT^U R\\D_G R_VZ[ RiDfG RcHfKlM R]NZT R\\QaQ^X RcH_KZN',
	    'Po\\IiI RRKXK RZLlL RRQXQ R]QiQ R]TiT RRVXV R]WiW RRERY RREXEXV R\\E\\I R]N]W RcCcL R]NiNiW R\\EiEiI RfYlZ R`YY[',
	    'Po\\HbH RfHlH RRKXK R\\LbL RfLlL RRQXQ RaSgS RRVXV RaWgW Rh[l[ RRERY RREXEXV R\\D\\[ RaPaY R\\DbDbL RfDfL RaPgPgW RfDlDl[',
	    'PoZFlF RZKlK RRKXK RbNhN RRQXQ R]QkQ RZTlT RRWXW RRERY RREXEXV R]H]Q R`C`H RbHbN RcRc[ RgChHhN RcTgXlZ RcT`WZZ',
	    'PoZEaE RRKXK RRQXQ R_QgQ RRVXV RRERY RREXEXV R[J[M R`M`P R[JkJkM R\\F]J RiMlP RiFfI R^TZV R`Q\\V R]NZP RbFbJ RaKeL R^TaWfYl[ R^ThTeWaYY[ RiC_E',
	    'PoRKXK RRQXQ R\\QkQ RRVXV R]WjW R]ZjZ RRERY RREXEXV R\\K\\Q R]S][ RcKcQ R]SjSj[ R\\KkKkQ RcCgG R_M`O R_IgHlI RgMfO RcC_GZI',
	    'Po\\IlI RRKYK RfLlL R_NiN R\\QlQ RRQXQ R_SjS R_UjU RRVXV R^ZjZ RRERY RREXEXV R^W^[ R^WjWj[ R_EYK RaJ^M R_EfFdH RfHfL R\\I\\RZX',
	    'PoRKXK RRQXQ R\\SaS RdSjS RRWXW RRERY RREXEXW R\\M\\S R\\MaMaS RdMdS RdMjMjS RhXk[ RfWiY R^WaZ RcCiI R^VY[ RfVb[ R^JgIlJ RbC]IYJ',
	    'PoRE^E R`FlF RSG^G RTL]L RWTgT RWWgW RWZgZ RTITL RWQW[ RYCYG RTI]I]L ReCeI RWQgQg[ RaIdMkO R`IjIgMaO R[M[ORP R^OVP',
	    'PoTG]G RaGkG RTI]I RaIkI RTL]L RaLkL RbSkS RUViV RR[l[ RTDTL RTD]D]L R_L_N R_V_[ RaDaL RaDkDkL RbObS RSNSPkNlP RZRTT',
	    'PoUG^G RaGjG RUJ^J RaJjJ RUM^M RaMjM RVQjQ RWSiS RWWiW RWZlZ RUDUL RWOW[ RUD^D^L R`O`Z RaDjDjL RYMSS RaDaK`N',
	    'PoRKWK R^KcK R]NdN RRQWQ RRVWV RRERY RREWEWV R[K[[ R^E^K RaCaK RcEcK RgRk[ RiReZ R[CYI R[IXQ R^QcQdXbX RfMjY RgDeLiW R^Q]Z RfIkIhV',
	    'PoYIaI RRKWK RRQWQ RRWWW R^[g[ RRERY RREWEWW R^D^R R_T_[ RgXg[ RbTdV R]JaM RiTkY RhNlR ReNaR R\\KXO RfCfKiP RjGiK R^G[M R[UZZ RcGbK RfJdP RaDZE RfGeN',
	    'Po^IgI RRKXK R[PaP RdPjP RRQXQ RZSkS RRWXW RRERY RREXEXV R[K[P R^D^I R[KaKaP RcQc[ RdKdP R^DgDgI RdKjKjP RcRhWlY RcR^XYY',
	    'PoRKXK R`LgL RRQXQ RZRlR R[UlU RRWXW R_[c[ RRERY RREXEXV R[F[I R`I`L RcCcF RcUc[ R[FkFkI RhIkL R]I[L RaHeH RaNaR ReNeR R\\NjNjR R\\N]R',
	    'Po[EfE RYGlG RRKXK R[PkP RRQXQ R[SkS RYVlV RRWXW RRERY RREXEXV R[J[P R`X`[ RcDcV R[JkJkP RhWl[ R^L_N R[XX[ RgLfN RdWe[ RiDbD',
	    'PoRKXK R]PkP RRQXQ R]SjS R]VjV RRWXW R]ZlZ RRERY RREXEXV R]O][ R[DaDaL RdNdZ RdDkDkL R\\F^H ReFgH R]OYS RaI[L RkIdK R^M^O',
	    'PoZFkF R\\KkK RRKWK R]MjM R]OjO RRQWQ R]RkR R[UjU RRVWV RRERY RREWEWV R]I]S R`C`G RdIdR RfCfH R]IZM R]VbXj[ RiVdY[[',
	    'Po[FlF RRKXK R^LhL R[OlO RRQXQ RRWXW RRERY RREXEXV R[J[L R`C`H RfCfH R[IkJkL RfUlZ RkRfT ReOhW RcO[S RbS[V RdU[Z RcSdXd[_[',
	    'Po\\IjI RRKXK R\\LjL RRQXQ R[TlT RRWXW Rd[i[ RRERY RREXEXV R\\E\\L R\\EjEjL R`W\\[ RfQkR R`WiWi[ RaT^Y R`P[R R[OlOlQ R[OZQ',
	    'Po^IjI RRKXK RRQXQ R]QjQ R]TjT RRVXV R]WjW RRERY RREXEXV R\\F\\I R]N]W RdCdF RdIdM R]OjOjW R\\FlFlI RgYl[ R`K[M RaY[[ RhLbN[O',
	    'PocJkJ RRKWK RcNkN RRQWQ RRWWW RjYlY RRERY RREWEWV R]U^Z RcQcZ RcFcN RfCfF RgNg[ RcFkFkN RcQlQlY R^MZQ R`TaY R^CZL R[UZ[ R`H_LYL R`NaRZR',
	    'Po\\DlD R^HlH RRKXK R_NjN RRQXQ R_QjQ RRVXV R`XiX Rh[k[ RRERY RREXEXV R^T^[ R_K_Q RbFbJ RdKdX RgFgJ R_KjKjQ R^TkTk[ RgViX R\\D\\QZY',
	    'Po\\FlF R^JkJ RRKXK R\\MlM RRQXQ R^SjS RRVXV RRERY RREXEXV RaGaM RdMdW RgGgM R^PjPjW RbCeF R^P^WhXl[ RbX\\[ R\\F\\QZY',
	    'PoUH]H RVMjM RWOiO RWQiQ RWTlT RSVgV RWLWS RUD]D]J R`L`S RaDjDjJ RXJVNRO RZF^F RfFjFfF RaFeFaEaJcIjIfJ RUFXFTEUJYJ RXW_Yk[ R\\JXK RcYT[',
	    'PoRKXK R\\NkN RRQXQ R\\QkQ R\\SkS R]VkV RRVXV RRERY RREXEXV R\\G\\J R\\L\\S R\\LkLkS RZHkHkK R_C[G R^EfEbI^K R`TZW RbJiJ R`Yl[ RiVdYY[',
	    'Po]GjG R]JjJ RRKXK R]LjL RRQXQ R[TlT RRWXW Rd[i[ RRERY RREXEXV R]E]L R]EjEjL RbRdT RbLdN R`W\\[ RfQkR R`WiWi[ RaT^Y R`P[R R[OlOlP',
	    'Po]DjD R]JaJ RfJjJ RRKXK RfMkM R\\MaM RRQXQ R\\RlR R]TkT RRVXV R]WkW R]ZlZ RRERY RREXEXV R[G[J R]P][ RcDcM RePeZ R[GlGlJ R^OZS',
	    'PocEjE RaJhJ R^JfJ RRKXK RcLiL RRQXQ R_QiQ R_TiT RRWXW R\\[l[ RRFRY RRFXFXW R^V^Z R_N_S RbVbZ RdNdS RfVfZ R_NiNiS R^VjVjZ R\\GkGkJ RcCbGcL R\\G[SZZ',
	    'PoeElE RRKWK R[LjL RRQWQ R]ThT RRVWV R]WhW R]ZhZ RRERY RREWEWV RZNZQ R[I[L R]Q][ RYE`D`G R`I`L ReIeL ReCeG R]QhQh[ R[IjIjL RZNkNkQ',
	    'PodFlF RZFbF RRKWK RYLcL RdNkN RRQWQ RdQkQ R[RaR RdTjT R[VaV RRVWV RdWkW Rd[l[ R^[a[ RRERY RREWEWV R[N[[ R\\H\\K R^C_F R[NaNb[ ReNe[ RdCdIkIkN RaH_L',
	    'PoZFlF RRKXK R\\MbM ReMkM RRQXQ R\\QkQ R\\TjT RRVXV R\\WjW R\\ZlZ RRERY RREXEXV R]P][ R_C_G ReOeZ RfCfG ReIkIkM RcNfP R^OYT R\\I\\M R\\IbIbM ReIeM',
	    'Po\\DbD R[FcF R\\HbH RdKlK RRKYK R\\KbK R\\PbP RVUhU RVXhX RVZhZ RVRV[ RVJVP R\\M\\P R\\MbMbP RhJhP RVRhRh[ RSFUH RdFgH RjEeJ RUCTG RXFSJ RgCfG RXLYO RkLlO RSLRP ReLdP',
	    'PoZDaD RdDlD RRKWK RZMlM RRQWQ RZSkS R]VbV RRVWV RRERY RREWEWV R[G[K R]S][ R`M`S R[GaGaJ RdGkGkJ RZPkPkS RfMe[lZl[ RcJcM RhHhJ R^H^J RkVeW RbZ\\Z ReGdK RZMZSYZ',
	    'PoTEjE RTNkN RRQ^Q R_QlQ RSZ^Z R`ZlZ RSSS[ RTGTN RYOWS]S]X R^C^G R`S`[ RfOdSjSjX RYL]L RYU]UYT RgUjUfT R\\I_IbH R_LbL RYGYKfIgIdIaI^H RYH[HgGgLdLaL^L RVTYUVTVXXW]WYX RcTfUcTcXeWjWfX',
	    'PoRKWK R]KiK RRQWQ R[RkR R[TkT RRVWV RYWlW RRERY RREWEWV RZGZJ R]I]L R`Y`[ RcCcG RcNcW R]IiIhL R[NjNkR RZGlGlJ RiXl[ ReYe[ R]D^F RhDgF R[YY[ RgOfQ R[N[R',
	    'Po[HbH ReHlH RRKXK R[KbK ReKlK R^NcN RRQXQ RRVXV R]XcX RRERY RREXEXV R[D[[ R^P^X R`N`P R[DbDbK R]PcPcZ ReDeK ReQhX ReQiQfY RfMdT R^UbU^U ReDlDl[i[ R^RbS^S',
	    'Po[GlG R^KbK RRKWK R^PlP RRQWQ RRVWV R_WhW R]ZiZ RRERY RREWEXV R^M^P R_T_W RcRcY ReHeL RgMgP R_ThThW R^MkMkP R[DlDlG R_SlRlZ RbO\\U Rk[h[ RgKjJkI R_IbI RaI^I RjIgI R[D[QZX',
	    'PoZ[`[ R`N`[ RRNlNfT RZHcL RUEhEfH_J R_NYTRW',
	    'Po`LhL RT[X[ RXMX[ RdClK RSE]EXI RUSRV RdE]K RUHZJ R^QjQe[ RXMTU RRM]M[Q',
	    'Po_PkP RT[X[ RXLX[ RbUg[ RdKfN RfGlM ReDgH RjRdX RSE\\EWJ RbH^M RUIXLSW RRM\\M[R RdCaJ',
	    'PoRIlI R[N`N RbSiS R[YeY Re[k[ RTPT[ R[U[Z R`I`N RTPkPk[ RjJfL RbPbS R\\SWU R[UeUeY RUEhE_G R_IXMRN RZGhI',
	    'Po_PjP R_UjU Rg[j[ RT[X[ RXMX[ R_K_[ ReCeK R_KjKj[ RhEkH RSE]EXI R`F]J RUSRV RUHYJ RXMTU RRM]M[R',
	    'Po^GlG R]LlL R`UjU R`ZjZ R`P`[ RbCbL RhChL R`PjPj[ RRE\\EXI RTHYJ RWMSV RRM\\M[R RXMX[T[',
	    'PoeHkH R^OjO R^QjQ R^SlS R]VjV RT[W[ RWLW[ R_L_S R]DcDcJ ReKeS RUHXK RRE[EWI R^L[P RWLRW RRM[LZQ R]D^J^O ReDeJiJ R`WeYl[ R]J_IcI_J ReDkDkJgJ RcF`F]F ReFhFkFhFeF RjWdY[[',
	    'PoWHjH RROlO RXCTK R_H_O]UYXR[ R_OaUeXk[',
	    'PoWNiN RRSlS RdEgG RSIhHkK RZFWI RVOTQ R_SdXj[ R[CYG RXJVP R^T[XWZR[ R_N^U',
	    'Po_HkH RSLZL R\\PlP RT[Y[ RSDZDZK RgVl[ RbU\\[ RdHdQhX RdNfU R`C]L RSKRQZQY[ RdLcU',
	    'PoUH_H RRO`O RbXlX RbFbZ RbFlFlZ RVCRL RYHYNWUS[ RYQ_W',
	    'PoUKlK R\\NiN RXSlS RZESL RYFcFaJ RbNbScVfYk[ R\\KZQ RaU^YY[ RVJVQUX',
	    'PoUG\\G R_KfK RRO\\O R_[d[ R^DfDfK RlCl[ RXGXR\\W RXQTY RUCSL RXLWV R_K^QfQe[',
	    'PoTH]H R^NkN RRO]O RgWl[ RXGXR]W RbV][ RVVRZ RdEeRiY RdQaX RXRUY RdLgW RUDSL RdNbW RjD^F RdJeT RXLWV',
	    'Po`ElE RTH]H RRO]O R`SkS R`YlY R`E`[ R`KkKkS RXGXR]W RVVRZ RXRTY RUCSL RXMWV',
	    'PoTH\\H RRN\\N R]OlO Rf[j[ RdQdX R`GjGj[ RXGWR[V RdJeM RWRTY RdCcG RUCSL RWLVV R`G_S^Y',
	    'PoTH[H R]JlJ RRN[N RhQh[ RWGWRZV RfKlQ RbL]Q RWRSY RdG`O R^Z\\[ RUCSK RaQ`X RWLVV RiD`E',
	    'PoUH[H RSO[O R]TkT R[[l[ RdCd[ RWGWRYU[X R_JbP RhJjMkP RVDRL RXOUX R_G\\Q RiFfP RWMVW',
	    'Po^ElE RTH\\H RRO]O R_QjQ R][l[ R_I_P R_IjIjP RhSgY R`SaY RUDSL RXHXOWUR[ RWS]V',
	    'PoTG[G R]HlH RRO[O R\\RlR R]EdEeN RWGWR[V RdH^N RdHhLlN RaWj[ RWRTY RcO_WhY RTCSL RhReY\\[ RWLVV RiC`E',
	    'PoTH[H R]HlH RRN\\N R`OhO RaXhX Rh[k[ R]Q][ RaJaO RaTaY RaThThX R_KiKiO R]QkQk[ RfHkL RTCSL RiD`E RcEaH]L RWHWPSY RWQZU',
	    'PoaEhE RTG[G RRO\\O R^PjP R^WjW R^ZjZ R^I^P R^S^[ RdJdP R^SjSj[ R\\JkIkP RWGWR[V RhFlJ R_G\\J R`LaN RgLfN RWRTY RTCSL RWOUX RWLVV',
	    'Po\\FlF RTH[H R^KkK R^MjM RRO[O R^OjO R^RkR R_I_R RaCaG RdIdR RgCgH RWGWRZU[X RWRSZ R`WeYl[ RTCSL RWOUX R\\UiUgWcY\\[ RWMVV',
	    'PoTHZH R\\IkI R\\NcN RSOZO R\\SbS R\\VbV Rf[l[ R\\P\\[ RfSf[ R\\DkDkI RfJfQlPkQ RlXl[ RbKcO R_J\\N RWGWRYV RVRSZ RkLeM RkTeW RaEaI RfEfI RUCSL R\\E\\I R\\PbPb[`[ RWMUW',
	    'PoSFlF RYYjY RYMY[ RXOjOj[ R]FXORT',
	    'Po^FlF RRF]F RUX\\X Ra[f[ RVKVZ RUN\\N\\X RfFf[ RWFSS',
	    'PoRFaF RVX^X Rf[l[ RVKVZ RUN^N^X RdGdU RlDl[ RWJRS RWFTQ',
	    'PoRE\\E RUX[X Rh[l[ RUKUZ RTN[N[X R`EhEi[ RlUl[ R`E`T\\[ RWESP R`N^X',
	    'PoSE\\E R^OlO RUX[X R`[e[ RUKUZ RTN[N[X ReKe[ R_FkEeK RWETP',
	    'Po^EkE RRE\\E R]NlN RUX[X RUKUZ RTN[N[X ReEe[ RWESQ',
	    'PoRE\\E R^FlF RUX[X Rg[l[ RUKUZ RTN[N[X RgFg[ RlWl[ R`W\\[ RWETP RaFaQ`W',
	    'PoSE\\E RUX[X Rd[k[ RUKUZ RUN\\N\\X RdFd[ RlWk[ RWESS RjD]G RhO^P RkN`P',
	    'PoRE]E R`IlI RUW\\W R_[k[ RVKVZ RUN\\M\\W RlXk[ R`MjM_X RbC^M RWESS',
	    'PoRF]F R_FkF RUY\\Y R]ZlZ RUKU[ RUO\\N\\Y ReFeZ RVGST',
	    'PoSE]E RUW\\W RVKVZ RUN\\N\\W RlJgU RcE_NdOhSbY^[ RbHlHiQ RWESR',
	    'PoRE\\E RUX[X Rh[l[ RUKUZ RTN[N[W R_EhEiZ RcMeR RWESP RlWlZ R_E_R^X',
	    'PoRE\\E R_GlG RUX[X RUKUZ RTN[N[X RdCfE RWESQ R_G_R]Y',
	    'PoRE[E RUWZW Rf[j[ RUJUY RTMZMZW RbP]U ReT^[ RhLdV RcL`R RVESQ R]EiEfHcI^LlLlRjZ',
	    'PoRE\\E R^EiE R]UhU RUXZX Re[j[ RUKUZ RTNZN[X RWESQ R`I_PlPj[ RiFhP',
	    'PoRE\\E RaSdS RUW[W RVJVY RUM[M[W ReCeR RiHlN RiPhUeX^[ RVFSR R`H^O',
	    'PoRE\\E R\\OlO RUWZW Re[i[ RUJUZ RTNZN[W R_EiEi[ RbHeK R_E^T[[ RVGSS R^N]W',
	    'PoSE\\E R\\PlP RUX[X RUKUZ RUN[N[X R]HjHjP RfUk[ RbU\\Z RcCcPgX RcP`W RWESR RcLbU',
	    'PoRE[E RUWZW R_[c[ RUJUY RTMZMZW RcDc[ RhTlX RkIfO ReLiV R`O\\W RcGgS RVFSR R\\KaK^V RcEfP',
	    'PoRF\\F R^IkI R]PlP RUX[X RUKUZ RUN[N[X RgVl[ RbU][ RdCdQhY RVFSS RdKcU',
	    'PoRE\\E R]IlI RUX[X RUKUZ RTN[N[X RcDdH R`McUgYl[ RhMeUaX[[ R_IbT RWETP RhIeU',
	    'PoRE[E R]ElE RfSjS RUXZX Rb[k[ RUKUZ RTNZNZX RbJb[ RaJjJjS RlWl[ RWESQ R]E]Q\\Y',
	    'PoTN\\N RRQlQ RXZiZ RYTY[ R_C_P RXUiUi[ R[QTY RhDXF R\\JVJ RiGYJ R^MXM RkK\\M',
	    'PoSE\\E R]HlH RUX[X Rg[l[ RUKUZ RTN[N[W R`LhLhZ RcCdF R]Z[[ RWETP RlVl[ R`L`S_X',
	    'PoSF\\F ReNkN RUY[Y RZ[l[ RUKU[ RUN[N[Y R_J_[ ReDe[ RVGSS',
	    'PoRE[E R^VbV RUWZW Rg[k[ RUJUZ RTN[N[W R_D_U RcRcV_[ RVFSS RcJ[K RbElElUk[ RfEfMeV',
	    'PoRE\\E RUW[W RUJUZ RTN[M[W RfUk[ RaU\\[ RdHdQgX RcQ`X R_HkHjO RWESR R`C]O RcLbT',
	    'PoRE\\E RUW[W RUJUY RUM[M[W RhMh[ RfFlL ReCfG RbF]L RcCaH RVFSR RaMaS_X',
	    'Po`EgE RRE[E RUWZW Rc[h[ RUJUY RTMZMZW R`V\\[ RhFkM R`G]M RVGRR RbN`W R^NiNiVh[',
	    'PoRE[E RUX[X RUKUZ RTN[N[X ReUk[ RhTa[ RcNgW RaMkMfV RWESQ R]EhEgM RaEaM_W',
	    'Po`ElE RRE\\E R]OlO RUW[W Rd[i[ RVJVY RUM[M[W R`E`O RiEi[ RaW\\Y RiOfQ_X RWESR',
	    'PoRE\\E R^LdL RUX[X Rf[k[ RUJUZ RTN[M[W R^D^Z RfDf[ RkJiMeN RVGRS RdY\\Z RlVk[',
	    'PoSE\\E RhLlL RUX[X RUKUZ RTN[N[X RaDhDhL R`PaTeXl[ R^M]M R]PiPgTcX\\[ RWETP R`E`L',
	    'Po^ElE RRE\\E R]OlO RUX[X RUKUZ RTN[N[X RiEi[ RWESQ RaEaR`X',
	    'PoRE\\E RhNlN R\\SlS RUW[W RUJUY RUM[M[W RdNd[ R]GhGhN RlKlN RVFSR RbCaK]O',
	    'PoRE[E R]GkG R\\LlL RUXZX RUKUZ RTNZN[X RcXg[ R`VdX R_QjQcX RWESQ RcC_Q',
	    'PoSE[E R]GlG R^OkO R\\VlV RUXZX RUKUZ RTNZNZX ReKe[ RWESQ RbC_O',
	    'PoRE[E R]GlG R^SjS RUX[X Re[l[ RULUZ RTN[N[X R^K^S ReCe[ RjKjV RlXk[ RWETP',
	    'Po^ElE RRE\\E RUX[X Rh[l[ RUJUZ RTN[N[X RcOeU RlWl[ RVESR R`K`P_T_[ RaEaLhKhZ\\[',
	    'PoRE\\E RUX[X Rf[k[ RUKUZ RTN[N[X R_E_R RgSgZ R_EjEjR RlVk[ RcU_Y\\[ RWESQ ReIdPcU',
	    'PoSE\\E RUX[X RUKUZ RTN[N[X RcKeM RdDlL RcF]L R^QjQe[ RdCaH RWESP',
	    'PoRE[E RTXZX RUKUZ RTNZNZX R^EiEkXl[ R`JgW ReL_X RVESQ RfHbT RiSj[ R^E^R\\Z',
	    'PoRF]F R_IkI R]PlP RUX\\X RaZkZ R]ZgZ RVKVZ RUN\\N\\X RiTl[ RWFSS ReCeObS`Z',
	    'PoRE\\E R\\HlH R]OkO RUW[W RUJUZ RTN[M[W RdCd[ RgTlY RaS[Y RdMhU RdMaU RVFSR',
	    'PoSE\\E R^IlI RbNlN RbTlT RUX[X RUKUZ RTN[N[X RbHb[ RVIRQ R`D]K RWETP',
	    'PoRE]E R`LjL R`SjS RUX\\X R[[l[ RVKVZ RUN\\M\\X R`D`[ R`DjDj[ RVFSS',
	    'PoRE]E R_EkE R_OkO RUX\\X R\\[l[ RVKV[ RUN\\N\\X ReEe[ RhSjX RVGSS RVETQ',
	    'Po^ElE RRE\\E R^TeT RUW[W Rc[i[ RUJUY RTM[M[W R^J^U R^JeJeT RiEi[ RVFSR',
	    'PoRF\\F RUX[X Ra[k[ RUKUZ RTN[N[X RaKa[ ReCeG R^GlGlK RlXk[ RjNaR RWFSS R^G^K',
	    'PoRE\\E R^HlH RgUjU RUXZX R_[k[ RUKUZ RTNZNZW R`K`[ ReJeW RjLjU RlWk[ R`C\\L RVGRS RjMcO\\Q',
	    'PoRE[E R^MlM RUYZY R\\[g[ RUKU[ RTNZNZX R^F^W RVESR RjD^F ReV\\W RfEfMgX RlVkZiZ',
	    'PoYG_G RbMlM RRPlP RYZiZ RUFUM RZSZ[ RbCbL RXUjTj[ RlJlM R]PXTRW RkFaH RYC[LTM R_KWL',
	    'PoSE\\E RdJlJ RUX\\X R_ZkZ RVKVZ RTN\\N\\X R_Q_[ RdCdQ R_QkQk[ RWHRQ RWETO',
	    'PoRE\\E R^LkL RUWZW R`[l[ RUKUZ RTNZN[W R^GkGkL RlXl[ ReT`Y RgReU RaQjPgS RdCeG RVESQ R^G^Q\\Y',
	    'PoRE[E R]MkM RUWZW RUKUZ RTNZNZW RgCg[ R]GkGkM R`V[[ RbCbS_Y RVGSS RkXiX R]M\\RlRkX RbN`W',
	    'PoRE\\E R\\NlN R^XlX RUXZX Rd[h[ RUKUZ RTNZNZX RbPfT RcGgK RWESQ R`EjEjOjUi[ R`E_I_L^P^W',
	    'PoSE\\E R^IkI R^QkQ RUY[Y R][l[ RULU[ RTN[N[Y ReIe[ RcDeG RWESQ',
	    'PoRE\\E R]HkH RUX[X R\\[l[ RUKU[ RUO[N[X RdDdH RVGST R`LaX RhLf[',
	    'PoRE\\E RUX[X Rc[l[ RVKVZ RUN[N[W RdFdL RfS`X R_V`Y R^LiLfT R^V\\[ RVFSS RjD]F RaZh[',
	    'PoRQkQ RXZhZ RYTY[ RWUhUh[ RZRSY RbFcIgMlO RUJ]N RXCTK RRF^F\\JZLTO R`EjEiIeL_O',
	    'PoRE\\E RUWZW R`[c[ RUJUY RTMZMZW RcDc[ RhTlX RkIfO ReLiV R]FaH R`O]X RcGgS RVFSR R\\LaL^V',
	    'PoRE]E R^EkE R]QlQ RUW[W RVJVY RUN[M[W ReEe[ RhHkN RaH^N RWESR',
	    'PoRE[E R^PjP RUX[X RUKUZ RTN[N[X RaUf[ RcKdN RdDlM RjQcX RbG\\M RcCaH RWESQ',
	    'PoRE\\E R_RfR RUX[X R_[k[ RVJVZ RUM[M[X R`R`[ R_LfLfR RlWk[ RaC]L RVFSS R_HlHlUiU',
	    'PoSE]E R_ElE RVX\\X RbYkY RVKVZ RUN\\N\\X RcLc[ RbOkOk[ RVKRS RdI_T RWETQ RdEaQ',
	    'PoRE[E RUXZX RUKUZ RTNZNZX RdCdN R`NbTgYl[ R^HkHjL R^NjNgTcY^[ RWESQ R^H^R\\Z',
	    'PoRE\\E R\\IkI R`UhU RUW[W RUKUZ RTN[N[W RdCd[ RhQlU R_R\\U RdHiS RdH^T RVGRS',
	    'PoRE]E R_QlQ RUW[W R_YlY RUJUY RTM[M[W R_H_[ R_HlHl[ ReCcH RVFSR',
	    'PoRE[E R^MlM R^SlS RUW[W RUKUZ RTN[M[W R^H^U ReCe[ R^HlHlT RWETP',
	    'PoRE[E R]ElE RUYZY R][l[ RULU[ RTOZNZY R]E][ RaKaU ReHeX RWESR RaKkKjUhU',
	    'PoRE[E R\\GlG RUX[X RUKUZ RTN[N[X RaCaJ RgCgJ R^LbSgYl[ RiLfR`X[[ RWESQ',
	    'Po_DlD RRE\\E R_JlJ RUW[W Rf[j[ RUJUY RUM[M[W RdPkOkY RdJdS`[ RVFSR R_D^Q]X RdMcW',
	    'PoRGlG RaNlN RRQlQ RYZjZ RYTY[ RbGbM RXUjUj[ R\\DZJWMSN R]QYURX RcDhE RhJZN',
	    'PoRE[E R^OjO RUYZY R\\[l[ RULU[ RTNZNZY RdOd[ ReFlM RdDfH RbH\\M RdCaI RWESR',
	    'PoSE\\E R_LlL R^SlS RUX[X Rh[l[ RUKUZ RTN[N[X ReEe[ R_ElEl[ RWETP R_E^R]Y',
	    'PoRE[E R_EfE R]NlN RUX[X Ra[d[ RUKUZ RTN[N[X ReHe[ RiRlY R`R]Y RWESQ R_F^N RjDaE',
	    'PoRE\\E R^HlH RUW[W RaYhY R_YfY RUJUY RTN[M[W ReCeG RhRl[ RVFSR RdKbY R^H^Q]Y RkXdY',
	    'PoSE\\E R_MkM RUX[X R^ZlZ RUKUZ RTN[N[X R^Q^Z R_F_M ReDeZ RkFkM RlQl[ RWESQ',
	    'PoSE[E R^RkR RUX[X R\\[l[ RUKU[ RTN[N[X RdRd[ RdKlO R^EiEcL\\O RWESQ',
	    'PoRE[E R_MiM R^SjS RUXZX R\\[l[ RULU[ RTNZNZX RdMd[ ReFlL RdCfG RdCbG\\M RWESQ',
	    'PoRE\\E R^IjI R\\PkP RUW[W RUJUY RTM[M[W RdCd[ RfTlY RaT\\Y RdNfT RcP`V RVFSR R_E]M',
	    'PoRE\\E R\\NlN RUX[X Rf[l[ RUKUZ RTN[N[X RdCdN RgNg[ RlWl[ R`W\\[ R^F`K RjEgK RVGRS RaN`X',
	    'PoSE\\E R^HkH R]NlN R^TkT RUY[Y R\\[l[ RULU[ RTN[N[X RdCdN RdPd[ RWESR',
	    'PoRE[E R`LhL RUXZX R_ZiZ RUKUZ RTNZNZW R_Q_[ R_QjQj[ ReFlM RdCfH RcG\\M RcCaI RWESP',
	    'PoRE\\E RUW[W R_ZlZ RUJUZ RTN[M[W R_H_[ R_HlHl[ RaMiT ReR`W RhJcT RdCcG RWESR',
	    'PoRF\\F R]HlH R^NkN RUX[X R_ZjZ RUKUZ RTN[N[X R_R_[ ReCeN R_RjRj[ RVGSS',
	    'PoSE\\E R]KlK R^OjO R^SjS R]WlW RUX[X RUKUZ RUN[N[X RdCd[ R^GjGjO RWESS',
	    'PoRE[E R]KlK RfUiU RUWZW Rb[k[ RUJUZ RTNZNZW RcNcZ RbNjNjU RkWk[ R`FgFeJ RVGRS RaC^J^S]Z',
	    'PoRE\\E R]HlH R^OfO RUX[X RUKUZ RTN[N[X RfVl[ RbK^O RdDeF RcHaL RhLbS]T RkPdW\\[ RWETP',
	    'PoSE\\E RUW[W R_ZjZ RVKVZ RUN[M[W R_R_[ R_RjRj[ RaGbKfOlQ RbC_J R`GiGgJbP]R RWETR',
	    'PoRE\\E R^JjJ R^OjO RUX[X RULUZ RTN[N[X R^E^[ R^EjEjO ReUjZ RcOfW RkReU RWESP RdY][',
	    'PoSE[E R\\NfN RUXZX Ri[l[ RUKUZ RTNZNZX R]EdEc[ RhFhU RlDl[ RWETP R_E_R]Y',
	    'PofElE RRE\\E ReOlO RUW[W RUJUY RUM[M[W RhEh[ R`W\\[ RWESR ReN]P R^EcEaQ`W',
	    'PoRE\\E RaIjI RbUiU RUX[X Rh[l[ RUKUZ RTN[N[X R^E^[ RbNbV RbNiNiU R^ElEl[ RWESP',
	    'PoRE]E R^IlI R]QlQ RUW[W RVJVY RUM[M[W RhIh[ R`DaH RiCgH RWESR RaIaR`X',
	    'Po\\ElE RSE[E RfRkR RUXZX R]YkY RUKUZ RTNZNZX R]J][ RbEbP RfEfR R]JkJk[ R`R^S RWESQ',
	    'PoRE[E R^GhG R]MlM RUXZX Ra[k[ RUKUZ RTNZNZX RaPa[ RcCcM RkXk[ RjG`Q\\S RiRaU RWESQ',
	    'PoSE[E R]JlJ R]SlS RUX[X RUKUZ RTN[N[X RaDaR RhDhR RgVkZ R`V\\[ RWESQ',
	    'Po^EkE RRE\\E RUX[X RUKUZ RTN[N[X R_J_U R_JjJjU RhXk[ RfWjY ReEcJ ReLdVaY][ RWESQ',
	    'PoRE[E R]GkG R\\QlQ RTXZX RUKUZ RTNZNZX RfVk[ RbU][ R_J`N RdCdQgX RiJgN RdQaX RVESQ RdLcU',
	    'PoSE[E R_GeG R\\RkR RUXZX Rf[l[ RUKUZ RTNZN[W RgRg[ RkLkO RlXl[ RaW][ RcKfNjO RhJ_N RaCcK RWESQ RbR`Y RhFaG',
	    'PoSE\\E RbThT RUX[X R^YlY RUKUZ RTN[N[X R^E^[ RbJbT RbJhJhT R^ElEl[ RWETP',
	    'PoRE[E R]JjJ R^SjS RUY[Y R^[k[ RULU[ RTO[N[Y R]E]J R_S_[ RdDdJ R]NjNjS RjEjJ RlWl[ RWESR',
	    'PoRE[E R]KlK RUXZX RUKUZ RTNZNZX RhQh[ RfKlQ RbL]Q RdGaO RaT_Z RWESQ RaQ`X RiD`E',
	    'PoRE[E R]HlH RUXZX Rc[g[ RUKUZ RTNZNZX RgHg[ R`V\\[ RdCdG RbHbR_Y RjMlU RWETP R^M\\U RbMaV',
	    'PoRE[E R]ElE R^SkS RUX[X R\\[l[ RUJUZ RTN[N[X RdSd[ RgLjO R^K`Q RcMeQ RjGhL R`G^L ReGcL RVESQ',
	    'PoRE\\E R_PlP R_UlU Rh[k[ RUKUZ R_K_[ ReCeK R_KlKl[ RkDjH R`EbI RVEUKRQ RUN[N[XUX',
	    'PoRE\\E R_HkH R]MlM RUX[X R_ZkZ RUKUZ RTN[N[X R_R_[ ReCeQ R_RkRk[ RWESS R`D^K',
	    'PoREZE RgMlM RcWlW RUXYX Rd[l[ RUJUZ RTNYMYX RcKcW RhEhW R_R\\Z RkDbF RVESQ R\\E`E\\M`M^W R\\Q^VaYj[',
	    'PoRE\\E R_KjK R_PjP RUX[X RVKVZ RUN[N[X R_F_Z RdCdG R_FjFjP ReUk[ RjTfU RkRhU RdPgX RWESS RfY\\Z',
	    'PoRE[E R]NlN RUWZW RbZkZ RUJUZ RTNZNZW R]G]J RdCdG R]GlGlJ RiUl[ RVGRS RaN^Z ReQbY RbJ_X',
	    'PoRE[E R_KjK R]QlQ RUXZX Ra[d[ RUKUZ RTNZNZX RdKd[ RiSkY RdDhHlJ R_S]Y ReC`H]J RWESQ',
	    'PoRE[E R^GgG R]KlK R]UlU RUWZW Ra[d[ RUJUZ RTNZNZW RcCcK RdSd[ R`Q\\S RjEhI_QiPfR RVGRS',
	    'PoRE[E R\\HkH RUWZW RUJUY RTMZMZW R_MaP RfVk[ RhNlR RbU\\Z RdCdQhY RiKfQ R_K\\R RcQ`X RVFRR RdKfV RdLbU',
	    'PoRE[E R\\JlJ RUXZX RdZkZ RUKUZ RTNZNZX RdJdZ RfDiG RlVlZ R^S[Z RiLcO RjPcS RWESQ RkTdW_X R`C`L^V',
	    'PoRE[E R]ElE R]IkI R\\NlN R\\TlT RUWZW RUIUY RTMZMZW RdEdN RhPh[ R`P`W\\[ RVESQ',
	    'PoSE\\E R^PlP R^UlU RUX[X Rh[l[ RVKVZ RUN[N[X R^K^[ ReKe[ R^KlKl[ RaGiK R_EkEiGeI RWESR',
	    'PoRE[E R\\GlG R]NkN R]SkS R\\VlV RUWZW RUJUY RTNZMZW R]J]S RdCd[ R]JkJkS RVFRR',
	    'PoSF]F R`IlI RUOXO RRQlQ RXZiZ RXCXO RYTY[ RgIgP RWViUi[ RWVRX R[QUX R^N\\O RjD`E R`G_M R\\JRK',
	    'PoRE[E R[LlL RUXZX R][`[ RUKUZ RTNZNZX R`E`Z RhEkH RgUb[ RjNfW RlWl[ RWESQ RcD\\F RdQ\\S RfCfMhY',
	    'PoRE[E R\\GlG R\\MkM RUWZW Ri[l[ RUJUY RTMZMZW RcPcZ RiPi[ RlXl[ RgJkN RaG_M RcCdG RVFRR R_Q^Y',
	    'Po]ElE RSE[E R]MkM R]RkR RUX[X RUKUZ RTN[N[X R]I]R R]IkIkR RcV_Z R^TbX[[ RaXk[ RWESQ RdEdQcV',
	    'PoRE[E R\\GlG R\\PlP RUYZY R_[k[ RULU[ RTOZNZY R_P_[ R`D`K RhDhK RcKfN RWESR',
	    'PoRE]E R_OlO R_UlU RUW[W Rh[l[ RVJVY RUM[M[W RfJfT R^KlJl[ RaG]K RcC_J_U][ RbFhFfL RWESR R_P^Y',
	    'PoRE\\E R^IjI R^MjM R^RjR RUW[W Rf[l[ RVJVY RUM[M[W R^D^R RgRg[ R^DjDjR RlWl[ RaW^ZZ[ RWESR RaR`X',
	    'PoRQlQ RXZiZ RYTY[ RcCcK RWUiUi[ RgEkI RXKSO RWVRX R[QUW RRGWH R^E\\I RUDYE RgJbMZO',
	    'PoRE\\E R`KjK RUX[X R^ZlZ RUKUZ RTN[N[X R^E^[ ReGeW R^ElEl[ RgOhQjT ReK`U RWETP',
	    'PoRE\\E RUW[W R_ZjZ RUJUY RTM[M[W R_R_[ R_RjRj[ ReJlQ RgDkH RbK\\Q R`F]J RcIaM RaD_H RVFRR',
	    'PoRE[E R^GkG R\\LlL RUXYX Rc[g[ RUJUZ RTNYMZX RdCdL RgLg[ RjOlW R^O\\V RVESQ RbLbSaX',
	    'PoRE\\FlG RUWZW Re[l[ RUJUZ RTNZNZW R`N`[ RaCaJ RfKf[ RgCgJ RlWl[ RhPeS RkNgR RaK^R RVGRS',
	    'PoRF\\F RdGlG RUXZX R^ZkZ RUKUZ RTOZNZX R^K^[ RdCdK R^KkKk[ RaNiV RfQ`W RgMeS RVGSS',
	    'PoRE[E R]HkH RUXZX RUKUZ RTNZNZX R]D]H RkDkH RbOeQ RgWe[ RdI_O\\P RdCdJgMlO R]SjSgX RWESQ',
	    'PoRE[E R_LhL RUYZY R[[l[ RULU[ RTOZNZY RdFlM RbG[M RjPe[ RcCaI R^Q_W RWESR RcPdV',
	    'PoSE\\E R^IkI R]LlL R`PkP R^UlU RUXZX RUKUZ RUNZNZX R^E^I ReCeI RePe[ RlElI RWESS R]L]S\\Z',
	    'PoREZE R[JbJ RTXYX RUKUZ RTNYNYX R_C_[ R_JaQ RiDkN RVESQ R_H\\S ReDcN RgMcZ RiTkZbZ',
	    'PoRE[E R\\FlF RUWZW RUJUZ RTNZMZW RfRfY RkKhN RdF`JcM_ObW RfNjV RaI[K RaO[Q RbKeR_V[X Rd[a[ ReIhT RdNfW ReFgR RVESQ',
	    'PoSE^E RSL^L RSP_P RRRlR RXZhZ RSESP RYUY[ RSH^H^L RWVhVh[ RbFdJhNlP R`EkEhJdO`P R[RWVRX RYLYP RYEYH',
	    'PoSEZE R]KcK RfKlK R]RcR RfQlQ RUXYX R_[b[ Rh[l[ RUJUZ RTNYMYW R]DcDcZ RfDlDl[ RVESR R]D]S[Z RfDfTe[',
	    'PoRE[E R\\GlG R^RjR R^VjV RUWZW Rd[k[ RUJUZ RTNZMZW R^N^W RdJd[ R^NjNjV RgHlM RlXl[ RaH[N RbC_K RVFRR',
	    'PoRE[E R\\JlJ R[OlO R\\UlU RUXZX R[[l[ RUKU[ RTNZNZX R_J_U R]FdEd[ RiJiU RVFSR RjDaE',
	    'PoRE[E R_RkR RUXZX R][l[ RULU[ RTNZN[X R^G^K ReRe[ RdCeG R^GlGlJ RfJkP RcJ^O RWESQ',
	    'PoRE\\E R]OlO R_TjT RUX[X R`[d[ RULUZ RTN[N[X RdJd[ R^KjKjU RbF]K RbC`I RaFhFeJ RWESP',
	    'PoSFkF RYIeI RYLeL RROlO RZZfZ RZUZ[ RYVfVf[ RfCeOiT R\\RUY RVSRU RYCYOTT RWRgRlT',
	    'PoRE[E R`LiL R]QlQ RUXZX Ra[d[ RUKUZ RTNZN[X R]G]J RdQd[ R]GkGkJ RhUkW R_U[Y RcCeG RWESQ',
	    'PoRE[E R]FkF R^IkI R\\LlL R_SjS R_VjV RUXZX Rf[j[ RUKUZ RTNZNZX R_O_[ RdCdL R_OjOj[ RWESQ',
	    'PoRE[E R[JbJ RdJlJ RUWYW RUJUZ RTNYNZW R_C_[ RgCg[ R^LbP RfNbV RgIkV RVGRS R_I[U RgHdT R_G\\S',
	    'PoSE[E R_MkM RUXZX R^ZlZ RUKUZ RTNZNZW R^E^[ RfHfW R^ElEl[ RhQjU RdNiR RcQ_U ReKaT RWETP RiG`H',
	    'PoSRkR RXZiZ RYUY[ RcCcI RWViVi[ RWKSP RTDXE RRHVH R[FkFkI R[RXVRY R^JaMeOlP R[JiJfMbO[P R[GZN',
	    'PoRE[E R]LkL RdSjS RUWZW Rd[l[ RUJUZ RTNZNZW R]G]J ReLeZ R]GkGkJ R`VcZ R_SaY RdCdG RVGRS R_Q]Z',
	    'PoRE[E R^IlI R_VjV RUXZX R_ZjZ RUKUZ RTNZNZX R^E^O R_Q_[ R_QjQj[ RlMlP RhNkP R^EeDgN RWESP RdN]N RjCcD',
	    'PoRE\\E RaJiJ RaNjN RbWiW RUW[W Rh[l[ RUKUZ RTN[M[W RbRbX ReGeN RbRiRiW R^ElEl[ R^E^T[[ RWESP R^N]X',
	    'PoRE\\E RbGkG R^HfH RdSlS RaSjS R^SgS RUW[W RUJUY RUM[M[W RkLkN RaDdKfMjN RaQdWfZj[ RlYk[ RdOhP ReDiD RVFSR RiJcL]N RiTfWaY\\[',
	    'PoRE[E R^JjJ R^WeW RUWZW Re[i[ RUJUZ RTNZMZW R^E^J R^R^X RdDdJ R^ReReV R\\NkNj[ RjEjJ RVGRS',
	    'PoRE[E R_JhJ R\\MlM RUWZW Ra[d[ RUJUZ RTNZMZW RdMd[ R^P`S RfTkY RkPhS RdOgV RbV\\Y RVGRS R`C_K R`FhFgN',
	    'PoRE\\E R_HjH R_LjL R]OlO R]SlS RUX[X Rd[h[ RUKUZ RTN[N[X R_D_L RhOh[ R_DjDjL R`UcX RWESP',
	    'PoRE\\E R]GlG R\\TlT RUW[W RUJUY RTN[M[W RdQd[ R`LbN RgLkP RgLcP R`I]Q RhIeO RdCdG RVFSR',
	    'PoRE\\E R^GkG R^MlM R`UjU RUW[W R`ZjZ RVJVY RUM[M[W R`Q`[ RbCbM RhChM R`QjQj[ RWESR',
	    'PoRE[E R^GjG R\\KlK RTXZX RUKUZ RTNZNZX RdCdK RbP\\U RfMkO RaSeXl[ R`N\\P R`RiRdW\\[ RVESQ',
	    'PoRE\\E R^KkK R^OkO R\\UlU RUW[W RUKUZ RTN[M[W R^F^O RdFdO RfQf[ R^FkFkO RdCcF R`Q_U RVFSR',
	    'PoRE[E R^LhL R\\RjR RTXZX R[[k[ RULU[ RTNZNZX RcLc[ RcDiJlK RcCbF[L R^T_Y RiTfY RVESQ',
	    'PoRE\\E R_IlI R_NkN R_TkT RUW[W R_ZlZ RUKUZ RTN[N[W R`G`[ RfGfZ RaC\\N RgCeJ RVGSS R`E_M',
	    'PoRE[E R^FdF R\\JlJ RUWZW RUJUZ RTNZNZW R^N^Z RbLbY RiDkG RlWk[ RVGRS ReQbQeQ RgCgOhZ RfY[Z',
	    'PoSE\\E R]FkF R^VeV RUW[W Re[i[ RUJUZ RTM[M[W R^Q^X R^QeQeV R\\MkMj[ RcHkK RcHaJ]L RVFSR RdCcI',
	    'PoRE[E R`KhK R]TjT RUXZX Rh[j[ RUKUZ RTNZNZX R]O][ RaOa[ RfOf[ R]OjOj[ RdDkL RcD\\L RWESQ',
	    'PoRE[E RhUjU RUWZW Re[l[ RUJUZ RTNZMZW R\\G\\J RdCdG RfLf[ ReLjLkT R\\GkGkJ RlXl[ R]PaT R`V\\[ RbP^Y RVGRR R_I\\R R^McM`W',
	    'PoSE[E R^NjN R\\UlU RUXZX RUKUZ RTNZNZX R^G^T RbCbT RfCfT R^GjGkT RgVl[ RaV\\[ RWESP',
	    'PoSE\\E R`LjL RaViV RUX[X Rg[l[ RUKUZ RTN[N[X R^E^[ RaOaV ReLeV RiOiV R^ElEl[ RbFcJ RWGRQ RhFgL RWEUO',
	    'PoRE\\E R^GkG R\\NlN RUX[X R_ZjZ RUKUZ RTN[N[W R_R_[ RdCdG R_RjRj[ RiIgM R`HaM RVGSS',
	    'PoRE[E R]FlF R^IkI R\\LlL RUXZX RUKUZ RTNZNZX R^O^W RdCdL R^OjOjW RfXl[ RWESQ RbY[[ RdRdW',
	    'PoRE[E RaHjH R^HgH R\\LkL RUXZX RUKUZ RTNZNZX RfLkP RfEjI RcC^I RdQ^S RfS_W RiV^[ RWESQ RcHbL]P[P',
	    'Po]ElE RRE[E R\\TlT RUWZW RUJUZ RTNZNZW R]I]P RbIbP RfIfP RdSeWhYl[ RdEbJ RcV`Y\\[ RdQbX R]IkIjPhP RVESQ',
	    'PoXHgH RSO]O RaOlO RSRlR RYZiZ RSKSO RXDXH RYUY[ RSK^K^O RaKaO RXDgDgH RWViVi[ RaKlKlO R[RXVRX',
	    'PoSE[E R[HlH RcLiL R^OkO R\\SlS RUXZX RUKUZ RTNZNZX R^D^O RcCcL RdOd[ RiDiL RdSjY RcS^YZZ RWESP',
	    'PoRE[E R]FlF R_MiM R_SjS RUXZX Ra[d[ RUKUZ RTNZNZX R]P]S RdSd[ R_IiIiM R]PlPlS RdCeF RWESQ R_I_M',
	    'Po]EkE RRE[E R^HjH R\\LlL RUWZW R]XlX Rd[h[ RUJUZ RTNZNZW RdCdK R^NjNjX RVGRS R[SkSi[ R^N]X RdPcW',
	    'PoRE\\E RdHjH RUWZW RaWhW Ri[l[ RUJUY RTMZM[W R]N][ RaRaY RdHdN RaRhRhW R`DjDjN R`D^NlNl[ RVFSR',
	    'PoRE\\E R^HkH R^LkL RUW[W R_XhX Rg[k[ RUJUZ RTN[N[W R^D^L R_Q_Z R^DkDkL RdShV RaN\\S ReQcV RWESS R_PlPlVk[',
	    'PoRE[E R\\FlF R[SlS RUWZW RUJUY RTMZMZW R^L^S R`C`I RgCgI R^LiLiS RcIcSeVhYl[ RbU_Y[[ RVFRR RcNbV',
	    'PoSE[E R^KlK R_UlU RUXZX RUKUZ RTNZNZX R^F_R_[ RdOdZ RhOhZ R^FlFlK ReCfF RWETP R_OlOl[j[ R^N\\Z',
	    'PoRE[E RUXZX R_ZjZ RUKUZ RTNZNZX R]F]I R_S_[ R^TkSj[ R]FlFlI RcCdF RbI]M R`KeQlT RaJiKcQ]T RWESQ',
	    'PoSE^E RSI]I R`JjJ R`NjN RRQlQ RXZiZ RXEXN RYTY[ R`E`N RWViUi[ R`EjEjN ReCeF RWVRX R[QUX R]MSN',
	    'PoRE\\E R^IjI R^MjM R]QlQ RUW[W RUJUZ RTN[N[W R^D^M R_Q_Z RdDdM R^DjDjM ReVj[ RjUfV RdQgX RVESQ ReY]Z',
	    'Po^ElE RRE\\E R_MjM R_RjR R_VjV RUW\\W RVJVY RUM\\M\\W R_I_V RdEdI R_IjIjV RhXl[ RaX\\[ RWESR',
	    'PoRE[E R]FlF R`JiJ R`MiM R\\QlQ RfVkV RUWZW R^ZlZ RUJUZ RTNZNZW R^Q^[ R`C`Q RgQgV RiCiQ RVGRS RbQaW',
	    'PoREZE R\\JaJ RiLlL R\\PaP RUJUZ R\\E\\[ RdDiDiL ReUj[ RcPgX RcPjPhVeYa[ RaD\\E RTNYMYWTX RVESQ RaUZV RdFcM',
	    'PoRE[E R]KcK ReKkK R]PbP R]UcU RUWZW RUJUY RTMZMZW R]D][ R]DcDcK ReDkDkK RgWl[ RiUdZ ReQiY RdPkPhW RVESQ',
	    'PoSE[E R_HjH R_LjL R]OlO RUXZX RUKUZ RTNZNZW R_D_L R_DjDjL R`R\\V RbU]Z RgSc[ RcS`X RVESQ Rj[g[ R`O_TkSj[',
	    'PoRE[E R^IjI R^MjM R\\PlP RdUjU RUWZW Rc[l[ RUJUY RTNZMZW R^D^M RdPdZ R^DjDjM R_WbZ R^T`Y R^T[[ RVESQ',
	    'PoRE\\E RUWZW R^ZlZ RUJUY RTMZMZW R^O^[ R^OlOl[ ReUhX R^I`M RbHeK RhHlL RjEhH RhQbW ReCcH RaSfU R_C]H RVFRR',
	    'PoRE[E R]IlI RUXZX R[[l[ RUKU[ RTNZNZX RbIb[ RfIf[ RjLjV R_CaH RhCgH R^L^UbT]T RfTjTfT RVFSR',
	    'PoREZE R]HlH R_LdL R_UdU RTXYX RUKUZ RTNYNZX R_O_W R_OdOdU RlWk[ RhDkF RgUa[ RjMeX RVESQ RgCgLgOhZ R]H]R[Z',
	    'PoRE\\E R]FlF RUWZW RgXjX RUJUZ RTNZNZW R]L]O R^Q^X RdCdF RdNd[ R^QjQjX R]LlLlO R`HaK RhHgK RVFRS',
	    'PoRE[E R^HjH R\\LlL RUWZW RUJUZ RTNZNZW R^D^H RdCdH RjDjH ReSgXk[ RcW_[ RjPhU RaP`U ReNfW RVESQ R^J^R\\X ReNcW',
	    'PoRE[E R\\GkG R^TjT R^WjW RUYZY R\\[l[ RULU[ RTNZNZY R^P^W RdDdN R^PjPjW RdGjM RdG]N RWESQ',
	    'PoRE\\E RdGlG R_NjN R_RjR R_VjV RUW[W RVJVY RUM[M[W R_K_V RdCdJ R_KjKjV RgXlZ RbX\\[ RWESR',
	    'PoSEZE R\\FcF R\\IbI R\\McM RhPkP R[TlT RUXYX RUJUZ RTNYMYX R_C_P RdElElN RdSeWhYl[ ReOcO RcV`Y[[ RVFSS RdQbX RhEeN',
	    'PoRE[E R^IlI RaRkR RaVkV RUWZW RaZkZ RUKUZ RTNZNZW RaNa[ ReEeN RaNkNk[ RWESP R^F^R\\Z RjD^E',
	    'PoREYE R^SfS R^YdY RUJUZ R^L^S RbHbY R^LfLfS RfVh[ RlWl[ RTNYMYWTX R\\DiDjVj[ RVESQ R\\D\\SZZ RfG_H RgX`Y',
	    'PoRE[E R_KiK R_SjS R_WjW RUX[X R\\[l[ RULU[ RTN[N[X R]G]J R_N_W R_NjNjW R]GlGlJ RdCdG RWESQ',
	    'PoSE\\E R_MkM R_QkQ R_UkU RUX[X R_ZlZ RUKUZ RUN[N[X R]F]J R`K`[ RfKfZ R]FlFlJ RaJ\\O RdC_M RWESR RfHeO',
	    'PoRE[E R\\FlF R_NiN RUWZW RaXgX Rh[k[ RUJUZ RTNZMZW R]Q][ R_I_N RaTaY RdCdF RaTgTgX R_IiIiN R]QkQk[ RVGRS',
	    'Po_ElE RSE\\E R_IkI R_MkM RUW[W Rf[k[ RUKUZ RTN[M[W RaUbY ReEeQ RhTjV ReUeX RWESR R^T]Z R_E_QlRk[',
	    'Po_DkD RRE\\E R^IlI R_OkO R_SkS R_VkV RUW[W RVJVY RUM[M[W R_L_V ReDeH R_LkLkV RiXk[ RbX\\[ RWESR',
	    'PoRE[E R^IkI R_NkN R^SlS RUWZW RUJUZ RTNZNZW RaSaZ RhKhR R^DkDkI RfVk[ RjUgW ReShY RVGRS RbKbQaV RfY_Z R^D^R\\Y',
	    'PoRE\\E R^IkI RaSkS RaWkW RUW[W Rg[k[ RUJUZ RTN[N[W RaPa[ RfJfO RaPkPk[ R^DkDkI RiKlN RcK`N RVFRS R^D^R\\Y',
	    'PoREZE RaHkH RbRjR RUWYW Rb[l[ RUJUZ RTNYMYW RbKbQ RjKjS RcDdG R[M^M^X[[ R]E^I R^Xd[ RVESQ RiDgHfReW',
	    'PoRE[E R\\HlH RUXZX RUKUZ RTNZNZX R\\O`S RdPhT R_C`G RhJeP RjNdZ R`J]P RbN\\Z RhCgG RVGRS RVFSQ RaVbZ[Z RjVkZdZ',
	    'PoRE[E RhNkN R]UkU RUXZX R]ZkZ RUKUZ RTNZNZX R]E]N R]Q][ RdQdZ R]QkQk[ RdElElM RgHeM RaHcK\\N RWESQ RbD\\E RgEfL',
	    'PoRE[E R]EcE ReHlH RbOgO RTXZX Ri[l[ RUKUZ RTNZNZX ReFeK RdTiTi[ RaKkKkN RlYl[ RVFSQ RfLfRkQkR RdTc[ R]E]R\\Y RaKaT_Z RjD`E',
	    'PoRE[E R]GlG R_NlN R`QjQ RUXZX R`ZjZ RUKUZ RTNZNZX R`T`[ ReHeT R`JjJjQ R`TkTk[ RcDfF RWETP R]G]R\\Z',
	    'PoRE[E R]FlF R\\QlQ RUWZW Rd[h[ RUJUZ RTNZNZW R]L]O RdCdG RdNdQ R]LkLkO R`W\\[ RaGaK RhHgK RVGRS RbQ`Y RaUiUi[',
	    'PoRE\\E RdHiH R`SjS R`VjV RUW[W Rf[j[ RUJUY RTM[M[W R]L]O R`O`[ R`D`L R`DjDjL R`OjOj[ R]LlLlO RWESR RdHdL',
	    'PoRE[E R^OkO R_TdT RUWZW Rf[l[ RUKUZ RTNZMZW R^F^O R_Q_[ RfQf[ R^FkFkO RlXl[ ReKhL ReDbF RaIgK RgHdK`M RlRfU RVFRS RdZ\\Z',
	    'PoRE[E R^KkK R^PkP RUWZW Re[l[ RUJUZ RTNZNZW R^F^P ReFeZ R^FkFkP R_W[[ RcCbF RhQgW RVGRS RaP_X RjTkVfV',
	    'Po^EiE RRE[E R\\TlT RUXZX RUKUZ RTNZNZX RcRc[ R\\LbL]Q R_XZZ RcS]Y RdNkQ R\\NbP RcShYlZ RdLkLhOdQ R_FiI RWESQ RhEbH]I',
	    'PoTEjE RYMhM RSP^P R`PlP RVZ]Z RbZjZ RWSW[ RZHZM RUU]T][ RcSc[ RXIhIhM RbUjUj[ RcS^X RWSRW RWPVV RdPaV R\\EYISK',
	    'PoRE[E R]GkG R^KkK R\\PlP R`TkT RUWZW R][l[ RUJUZ RTNZNZW RdGdO ReTeZ R]WZ[ RhCgF R`CaF R`Q\\Y RVGRS',
	    'PoRE[E R_HkH RaMjM RaQjQ RaTjT R^VlV RUWZW RUJUZ RTNZNZW R^L^V R_D_H RaJaT RaJjJjT RgYl[ RbX\\[ RVGRS RiD`F',
	    'PoRE[E R^JkJ R^NkN R]QlQ RUXZX R^ZjZ RUKUZ RTNZNZX R]G]J R^T^[ RdHdT R^TjTj[ R]FlFlI RcCfF RWETP',
	    'PoRE[E R\\RkR R\\UlU RUWZW RUJUZ RTNZNZW RhNlR RhFlJ RgK`Q RcFdI RbJ_McO RcUgYl[ R^E\\K RdSbW`Y[[ RVGRS RjD^E',
	    'PoRE[E R[IlI R[PlP R\\SkS RUWZW RUKUZ RTNZNZW RaEa[ RfEf[ R\\LkLkS ReSlY RaSZY RVGSS RdD\\F RjDdE',
	    'PoSK]K RSSkS RXZiZ RUEUN RYUY[ RYLYO RYCXE RWViVi[ RhHlGlH RbKeNkQ R`KjKgN`Q R[SWWRY RYGXI RbDhDhI RbDaH RUE]E]QZQ',
	    'PoRE[E R^IjI R\\MlM R^UjU RUXZX R[[l[ RUKUZ RTNZNZX R^D^I R^P^U RdCdI R^PjPjU RjDjI R`WaZ RhVfZ RVESR',
	    'PoRE\\E R`IjI R_OkO R_SkS R_VkV RUW[W RUJUY RUM[M[W R_K_V R`D`H R`DjDjH R_KkKkV RgXkZ RbX\\[ RVFSR',
	    'PoRE[E RbMjM RbWkW RUWZW Rb[l[ RUJUZ RTNZMZW RbFbX RfCeF RbFjFjL RbPkPkX R\\P_P_Xb[ R\\J^L R]D`G R_W[[ RVESQ',
	    'PoRE[E RdGlG RbLlL R\\SlS RUWZW RUJUZ RTNZNZW RcOc[ RdGdL RhChP RcRkZ RcR\\Z R\\I`L\\P R_D[K R^GbG^O RVGRS',
	    'PoRE[E R^GjG R\\KlK R]QjQ RUXZX R[[l[ RUKU[ RTNZNZX R]T][ RbTb[ RdCdK RfTf[ R]TjTj[ RgMkQ RbK_P RVFSR',
	    'PoRE[E R_UkU RUW[W R_ZkZ RUJUZ RTN[N[W R_Q_[ R]DcDcM R_QkQk[ ReDlDlM RiFfI RjJfM RdOdQ RaJ^M R`G\\H RVFSS',
	    'PoSE[E R\\FlF R^MjM R^PjP R\\TlT RUXZX Rc[g[ RUKUZ RTNZNZX R^I^R RdCdQ RgRg[ R^IjIkQ R`VbX RgDjD RVESQ',
	    'PoRE[E R_OkO R\\SlS RUXZX R^ZkZ RUKUZ RTNZNZX R^U^Z RdNdZ RdGdK RkUk[ R_M\\P R^G_K RjFhK RWESQ RjD`E',
	    'PoRE[E R\\FlF RaPiP RTXZX RUKUZ RTNZNZX R`S`[ RfUk[ RfMjQ RgHkK RdCeF RkShU R`H]K RcJ]PePgX RcP`S\\U RVESQ RfY_[',
	    'PoRE[E R]FlF R^LjL R^OjO R\\UlU RUWZW Rc[g[ RUJUZ RTNZNZW R^I^O RdCdR RgSg[ R^IjIkO R`WbY R]RiQkR RVGSS',
	    'PoSE_E RbIlI RTJ^J RTL^L RRO_O RRRlR RYZiZ RTGTL RYUY[ RYCYQ RTG^G^L RhIhP RXViVi[ R[RXVRY RbDbJaO RjCbD',
	    'PoRE[E R_OhO R\\SkS RUXZX R`[d[ RUKUZ RTNZNZX RdSd[ RhVkX R^HaI RgKlP RjIhL R^W[Y R\\K`K\\P RdEhM ReFlFiJ R^C\\I R^FcF_M RWESQ',
	    'PoREYE RZGcG R[KcK RZOcO RUJUZ R_D_O RhVc[ RfRjZ RTNYMYWTW Ra[^[ RgCdLgV RVESQ R]O\\Y R]TbTa[ ReIkIjMhV',
	    'PoSEZE R]HlH R\\OcO RfOlO R\\TcT RfUlU RUXYX R`[c[ RUJUZ RTNYMYW R\\KcKcZ RdCdH RlEkI R]E]I RVFSR RfKlKl[i[ R\\K\\T[Z RfKfTdZ',
	    'PoRE[E RUWZW RUJUZ RTNZNZW R]DbDbL ReDkDkL RgFdG RhJdK R_F]H R`I\\M RdMgPlR RcM`Q\\R RdR^T RgT_X RVGRS RiWdY][',
	    'PoRE[E RdGlG RUWZW R^ZkZ RUJUZ RTNZNZW R^K^[ RdCdK R^KkKk[ R_RaS RdWeX RaOhW RbMdO RhRiS ReR`W RgMdS RVGRS',
	    'PoRE[FeF R^JcJ R[QeQ RbUeU RUWYW R\\YdY RUJUZ RTNYMYW R\\Q\\[ RcCcQ ReJlJlZ RVFSR R^C^Q^V Rk[h[ R^McMbU RhChOfY',
	    'PoSE[E R\\GlG RUWZW RUKUZ RTNZMZW RfVk[ R]PaT R]IaM RfPkT RfIjM RaP\\U RjIfN RkPgT RaI\\N RdCdQgX RdQaV^Y[[ RdNeU RWESR RdLcU',
	    'PoREZE R^GjG RUXYX RUKUZ RTNYNYX ReLkKkM RfDjH RhHeK RcC^G R^H\\L RcMlQ RcM[Q RdQ]S RgS^W RVESQ RjVcY][ RaJaL[L',
	    'PoRE[E R\\LbL RiMlM R\\PbP RUWYW R^[a[ RUJUZ RTNYMYW R\\HbHbO ReDeL ReDiDjM RfVl[ RiUc[ RdQhX RcPkPgX RlJlM RVESQ RbD\\E\\S[[ R\\SaTa[',
	    'PoRE[E R^HkH R^LkL RbTkT R^ThT RUXZX Ra[e[ RUKUZ RTNZNZX R^E^L RdEdL ReTe[ R^EkEkL RhWlZ RhRlU R`W]Z RcL_PeP]T RWESQ',
	    'Po\\DlD RRE[E R]MkM R^PjP R\\TlT RUWZW R`[d[ RUJUZ RTNZNZW R]H]M RbDbM RdTd[ RfDfM R]HkHkM RhVlZ R_V[Z RVGRS',
	    'PoSE\\E R]EkE R^HjH R\\JlJ R_PjP R_TjT R_WjW RUW[W RVJVY RUM[M[W R_M_W RdCdJ R_MjMjW RgYl[ RbY\\[ RWESQ',
	    'PoTFlF RWJ`J RbJlJ RWQlQ R\\ZjZ R\\T\\[ R\\G\\O R`C`F RgGgO RgIlN RfJbN R\\JWN R[K`M R_Q[VUX RTFTSS[ R\\UjUj[',
	    'PoRE[E R\\HlH R^PfP RUWZW R_[b[ RUKUZ RTNZNZW RbIbZ RiDkF RhVdZ RdSeV RlXk[ RjOfY R\\H]T^X RkLhV RVGSS RaLdLaL RgCgNgPiZ R\\P[Z',
	    'PoRE\\E R^IkI R_OlO R_RkR R_VkV RUX[X R_ZlZ RUKUZ RUN[M[X R^D^I R`M`[ ReCeI RfMfZ RkDkI R`K]Q RfKeO RVFSR',
	    'PoREZE RfSkS RUJUY R^L^[ RcWfZ RcGeJ RhHlK RbRcX R_C\\I R_I\\P RiEfL RdDaL RVESQ RbQ`[ RgNfZl[ RTMYMYWTW',
	    'PoSE^E RTH^H RhHlH RTN]N RSRlR RXZiZ RYUY[ RYCYG RTJ]J]N RWViVi[ RbJcMjP R[RXVRX RYJYN RbDhDhI RbDaH R`KjKgMdO^P RTJSP',
	    'Po]ElE RREZE R`HjH R]JkJ R_TjT R_WjW RUXYX Rf[j[ RUKUZ RTNYNYX R_Q_[ RaJaO R_QjQj[ RkMhM ReJgMlO RVESR ReN`O R]E]R[[',
	    'PoRE[E R]HkH R_NhN R]VkV RUXZX Rh[k[ RUKUZ RTNZNZX R]Q][ R]D]H RbQb[ RdCdH RfQf[ RkDkH R]QkQk[ RdJlN RdJ\\N RWESQ',
	    'PoRE[E R\\NbN R\\QlQ RUWZW RUJUY RTMZMZW RfXi[ R_TbW R\\H_J ReGhJ RlXk[ RaKbO RhOjP RjGgN R`G]N R_S[Z RhDfH RjSeY`[ R^D]H RVESQ RdCdLfX RjKkNeN',
	    'PoRE[E R\\GkG R]JjJ R\\NlN R\\RlR RUWZW R^ZjZ RUJUY RTMZMZW R^U^[ RdGdM R^UjUj[ R_C`E RhOfR RhCfG RVFSR',
	    'PoRE[E RUXZX Re[i[ RUKUZ RTNZNZX R\\N\\Q R\\NlNlQ R_HbK RhHlL RcW^[ R\\F]H ReFfH RkEjH RbEbH R^K\\L RWESQ RdQbX R`C_J RhCgJ R^SjSi[',
	    'PobDlD RRE[E RcLkL RbPlP R\\SlS RUXZX RUJUZ RTNZNZW RcDcP RdQd[ RcHkHkL RdSjZ RdS]Z R_L^P R\\H_I R]D`E RVGRS',
	    'PoRE[E R]PjP R^WiW RUWZW R^ZiZ RUJUZ RTNZNZW R^H^P R^S^[ RcJcP R^SiSi[ R\\JjIjP R_G[K R`K`N RaEgDjJ RgKfN RVGRS',
	    'PoRE[E R`KgK R^TiT RTXZX R[[k[ RUKU[ RTNZNZX R^O^T R^OiOiT RgIlN RiDfG RkHhJ R\\G_J\\N R`Va[ ReDhK RgVe[ R]EbE^L RVESR',
	    'PoSE[E R]IcI RfIlI R]McM RfMlM RaShS RaWhW RUXYX Rh[l[ RUKUZ RTNYNYW R]D][ RaOaW R]DcDcM RfDfM RaOhOhW RfDlDl[ RWESP',
	    'PoRE[E R^JcJ ReJkJ R^NkN RUWZW RUJUZ RTNZMZW R^E^N RbUbY ReEkEkN RdUfX RgTiV RcN\\U R_V]Z RcD^F RVGSS R_RlRk[g[',
	    'PoRE[E R\\HlH ReQlQ RdWlW RUWZW RUJUZ RTNZNZW RdCdN ReQeW RiOi[ R^D`G RjDgG R]TaV\\[ RdHhLlM R_O\\U R^RcQ`X RdH`L\\M RVGRS',
	    'PoRE[E R\\FlF RfMkM R]OkO R]RkR R\\UlU RUWZW RUKUZ RTNZNZW R]I]R RfFfL R]IkIkR R`DbFbK_M RhDeF RdUgYl[ RdScXaY\\[ RVGSS',
	    'Po]ElE RRE[E R]LkL R^QjQ R^TjT R\\WlW RUWZW RUJUZ RTNZNZW R]H]L R^N^T RbEbL RdTd[ RfEfL R^NjNjT R]HkHkL RVGSS',
	    'PoRE[E R\\FlF RaIhI R\\LlL R^SjS R^VjV RUWZW RUJUZ RTNZNZW R^O^V RaCaI RdLdV RhChI R^OjOjV RgXkZ RaX[[ RVGRS',
	    'PoRE[E R\\JlJ R^VjV RUWZW R^ZjZ RUJUZ RTNZNZW R^R^[ R]EdEdP RdRdZ R^RjRj[ RdIiOlP RdJ]P R`F`I RhFhI RVGSS RiC`D',
	    'PoRE[E R^GkG R^JjJ R^MjM R^PkP R\\SlS RUWZW RUKUZ RTNZNZW R_F_P ReFe[ RdSkZ RdS]Y R_C]I RVGSS ReEdJ',
	    'PoRE[E R^FjF R\\IlI R\\MbM ReMkM RdQlQ R\\TlT RUWZW Rf[l[ RUJUZ RTNZNZW R_K_Q RdCdI RgTg[ RhKhQ RlXl[ RaT_X^ZZ[ RVGRS RcP\\Q',
	    'PoRE[E R]IbI ReIkI R]OjO R]SjS R\\WlW RUWZW RUJUZ RTNZMZW R]D]I R]L]S R]DbDbI RdLd[ ReDeI R]LjLjS ReDkDkI RVESQ',
	    'PoUElE RWK_K RaKkK RVRlR R[ZiZ R\\T\\[ R\\G\\P RfGfP R[ViVi[ R[K_N RfJlP R^RWX RfJ`P R\\KWP R^GWH RiGbH RUEUQSY',
	    'PoRE[E R]HcH RfHlH R]LcL RfLlL RbRgR RbVgV RUWZW ReZgZ Ri[l[ RUJUY RTMZMZW R]E][ R]EcEcL RfEfL RbOgOgY RfElEl[ RVFSR RbObUaZ',
	    'PoRE\\E R^IkI R^LjL R^PjP R^TlT RUWZW RUJUZ RTNZNZW R_G_T RaWb[ ReGeT RjWk[ ReWg[ R`C\\K RfDeI R]V[[ RVGRS',
	    'PoRE[E R\\FlF R^IjI R_OiO R\\SlS RTXZX R_[j[ RUKUZ RTNZNZX R_V_[ R_K_O R`QaS RdCdI R_KiKjO R_VjVj[ RgPgT RVFSQ',
	    'PoSE[E R_MiM R^TkT R]WlW RUXZX Ra[e[ RUJUZ RTNZNZX R]G]K R_I_M RdCdG R^PfPe[ R_IiIiM R]GlGlK R_D`G RjDgG RVESQ RjOcP',
	    'PoREZE R[HkH R_OhO R`XgX Rg[k[ RUJUZ R\\Q\\[ R`J`O R`T`Z R`TgTgX R^LhKhO R\\QkQk[ RfHkM RaH[N R\\EcE_L RTNYMYWTX RVESQ RhDaE',
	    'PoREZFcG R[MbM R[[^[ RUJUZ R[I[M R_S_[ R[IbIbM R[QbP_S RhVc[ R^C_G RgRjZ RjPfY RTNYMYWTX RfGfLhW ReJkIhV RVESQ RfCdO RcUZV',
	    'PoRE[E R]SbS ReSkS RUWZW RUJUY RTMZMZW R]M]S R]MbMbS ReMeS ReMkMkS RgWk[ R_WbY RdCjI R`JhIlJ R_W[[ RgWc[ RdC]J RgUeZ R_T]Y RVFRR',
	    'PoRE[E R_MiM R\\UlU RUWZW RUJUZ RTNZNZW R^F^R RdHdQ R^FjFjR RcMfP RdL_Q ReVgYl[ RcCcF RfJfK RcSbW`Y[[ RVGRS RgH`I',
	    'PoREZE R`HlH Ra[l[ RUJUZ RhPhX RgOkT ReH`M R[M^M^X[[ R\\E]I RbCcG R^Xc[ RiCgH ReMbQ`Q RjKfNhQcU_V RfYcY RdJgOgU RVESR RTNYMYXTX',
	    'PoREZE R\\JbJ R\\NbN R[QdQ R^[a[ RUJUZ R\\F\\N R^C]FcFcN RgRkZ RiQeZ RfMiX ReJhV ReJkJhV RVESQ R^Q\\Y RfCdN R^UbUb[ RTNYMYWTX',
	    'PoRE^E RhIlI RTI]I RTK]K RRM^M RRRlR RXZhZ RTGTK RYUY[ RXCXP RTG]G]K RTNTP]O]P RWVhVh[ R[RWVRX RbDhDhI RcMgOlP RbDaH R`KjKfN_P',
	    'PoSEZE R]IkI R]LjL R[PlP R\\TkT R[WlW RVJVZ R]D]I RaDaI RdIdO RdTd[ RfDfI R]DkDkI R`QaT RgQfT RWESR RUNZMYWUX',
	    'Po]EkE RRE[E R^KbK RfKjK R^NbN RfNkN R^VkV RUWZW R^ZkZ RUKUZ RTNZNZW R\\H\\K R^R^[ RdEdP RdRdZ R^RkRk[ R\\HlHlK RVGSS',
	    'PoSEZE R\\FlF R^LjL R^OjO RUXZX R^YiY RUKUZ RTNZNZW R^I^O R_S_Y RaCaG RgCgG R^IjIjO R_RlRlY RdThX ReSaW RbO\\T RWESP Rj[g[',
	    'PodGlG RdUjU RUWZW Rc[l[ RUJUZ RTNZNZW R`C`N RdPdZ RhChN R`GbK R^Wc[ RhGkL R\\PkPjS R`G]L RhGeL R]ZZ[ RVGRS R_S]Y RRG[G R]GcG',
	    'PoRE[E R[HlH R\\MkM R]QjQ R\\TlT RUWZW RUKUZ RTNZNZW RbCbH RcMc[ ReCeH R]D_F RjDhG RgIfL R_I`L RcThXlZ RcT_XZZ RVGSS',
	    'PoUHZH RVK[K RdKjK RURjR RXZiZ RSNSQ RVEVN RYUY[ RWViVi[ ReDjDjM RSNlNlQ R_JaM RaD^G RaI^L R]EaF R[RWVRY RZDUD ReHjHiN',
	    'PoRE\\E R`OjO R^VkV RUW[W R^ZkZ RUJUZ RTN[N[W R]H]K R^S^[ RdSdZ ReCeH R`KjKjO R^SkSk[ R]HlHlK R_DaG RiDhG RVFSS R`K`P',
	    'PoRE[E R]FkF R^JjJ R\\MlM R\\SlS RUWZW R][`[ RUJUZ RTNZMZW R`O`[ RdFdM RlXl[ RiOkQ R_DaE RiDgE RfUi[ RjUfYbZ RVESQ RcO\\P RdV\\W RfNgY',
	    'Po]EkE RRE[E R^JjJ R^MjM R\\PlP R^UjU RUXZX R^XjX R[[l[ RUKU[ RTNZNZX R^R^X R^G^M RdGdM RdRdX R^GjGjM R^RjRjX RVESR',
	    'PoREZE RdGlG R\\LbL RdNlN RdTlT RUXYX RUJUZ RTNYMYX R\\E]R^[ R\\EbEbK ReIeL RhNh[ RgDiG RjIiN R^QbQbY]Y RVESQ R\\M[X',
	    'Po`FlF RTH]H R_JlJ R`MkM RUO]O RSRlR RXZhZ RTDULUO RYUY[ RUK]K]O ReJeP ReCfE RWVhVh[ RbGcI RhGgJ R[RXVRX RTD]E]I RTGSO',
	    'PoRE[E RbGfG R]J`J RgJjJ R[UlU RUWZW RUKUZ RTNZNZW R`D]E]U RbObU RbCbKeKeU RgEjEjU RgWk[ R^YZ[ R_W\\Z RVGSS R]O`P]P RhPjPgO',
	    'PoREZE R_HiH R^RjR R^TjT R^WjW RUXZX RUJUZ RTMZMZW R_N_W RdHdM R[OjOjW R]F]GlFkH RdCdF RgXl[ RaK\\L RbX\\[ RhK\\O RVESQ',
	    'PoRE[E R]HkH R]KkK RdUkU RUXYX RUJUZ RTNYMYX R]D]K RaWa[ R]DkDkK RgMdP RjOdT R_M\\P RbO\\UbTbU RfWf[ RjWl[ RjRkU R\\W[[ RVESQ RaP\\P RiPdQ',
	    'PoRE[E R\\ObO RgSlS R[SbS RUJUZ RcMkMkP ReWgZ R^N^UaX RcDkDgI RdTeY RdHiJ R]C\\JbIbK RaE\\G R\\L[Q RhLgZlZ RVESQ RdQbZ R^Q]X RTNYMYWTW',
	    'PoRE[E R]FlF R^JkJ R\\MlM R]RjR RUXZX R[[l[ RUKU[ RTOZNZX R^U^[ RaCaH RbUb[ RdHdM RfUf[ RgCgH R^UkUk[ RgOkS RbM`R RVFSR',
	    'Po\\EdE RREZE RfHlH R\\McM RfOlO R\\PdP RTXYX R[[l[ RUJUZ RTNYNYX R\\E\\P R]T][ RaTa[ R\\HcHcM RfTf[ R]TjTj[ R`M`Q R`E`H RgDeL RVESR',
	    'Po]EbE RVHZH RdHhH RVKZK RdKhK RRNlN RYZgZ RVEVN RYUY[ R]J]N R]C]HaHaN RXVgVg[ RdDiDiN RdNiS RXRfRlT RZNVRRT R\\RWWSX RZCUD',
	    'Po]EkE RSEZE R^JbJ R\\PlP RUXZX RUKUZ RTNZNZW R\\H\\K R]T][ RbSb[ RdEdN RfTf[ R\\HlHlK RfLkM RbM]M RjJfJ RWESQ R]TkTk[i[ RcPbW',
	    'PoRE[E R]FlF R`LiL R^OkO RUWZW RUKUZ RTNZNZW R]I]L RaCaH RgCgH R]IlIlL RgUlY RkRgT ReOhW ReO]S RcS]V RdUbW\\Z RVGSS RdSeXe[a[',
	    'Po\\EbE RREZE R\\IaI R\\MaM RUJUZ R]T]X R_E_Q RhWl[ RdFhGkK RhGcL RgVb[ RfQjY RjOeY RiCgI ReMhW RjMgV RTNYMYWTW Ra[][ RVESQ R[TZY R\\E\\QbQbVa[',
	    'PoRE[E R]ElE R]RcR ReRkR RUXZX Ra[h[ RUJUZ RTNZNZW RbUb[ RaCaH RhXh[ RcTeW RjOkS RaK`O R\\K`N\\R RjLhN RgCgHeLhNeR RbOcR RjVlZ R_H^L R^V\\Z RVGRS',
	    'PoSE[E R]FkF R\\JlJ R^OkO R^RkR R]VlV RUXZX Rd[h[ RUKUZ RTNZNZX R^M^T RbCbH RdHdT RgCgH RhUh[ R^MkMkT R`XbZ RVESQ',
	    'PoRE[E R]GkG R^IjI R\\LlL R^RhR R^UhU RUXYX RUJUZ RTNYMYX R_O_[ RcMcP R]PiPiU RgMlR R_DaE RaL[R RjVfX RhDeF RcWkZ RcG`N RcZ]Z RVESQ',
	    'PoSE[E RfFkF R^NjN R^RlR RUXZX RUKUZ RTNZNZX R^I^N R]FbEbH RfCfH R^IkIkN RlXl[ RfXi[ R_TbW RgOiP RjTeY`[ RbIbN RfIfN RWESQ RdOfX R]S]Z',
	    'PoREZE R]FlF R^TjT RUXYX Ra[d[ RUKUZ RTNYNYX RaCaH RdTd[ RgCgH R]O`O RhVlZ RaPgOlS R_LbM\\S ReJkJiN R`V]Z R_H]M ReIhP R^JcJ`P RWESQ',
	    'Po\\DcD RREZE R\\IcI R\\LcL R^QiQ R^TiT R^WiW RUWZW RUJUZ RTNZMZW R\\D\\L R^N^W R`D`G R_I`L R\\FcFcI R^NjNjW RgHkM RkGfL RfEhJ RfYl[ ReDkDiJ RaY[[ RVESQ',
	    'PoRE[E R]FlF R]IlI RbMhM R^OlO R_TkT RUWZW R_WkW RUJUY RTMZMZW R_Q_W RbFbL ReCeF ReOeW RhFhL R_QkQkW RhYl[ RbY][ RVFRR R]F]Q[Y',
	    'PoRE[E R^IjI R^MjM R\\UcU ReUlU RUXYX R\\ZcZ ReZlZ RUJUZ RTNYNYW R\\P\\[ R^D^M R_P_Z R\\PcPc[ RdDdM RePe[ RiPiZ R^DjDjM RePlPl[ RVFRR',
	    'PoWFhF RWIhI RSM^M RaMlM RSP^P RaPlP RSRlR RXZiZ RSKSP RWDWI RXKXP RYUY[ RSK^K^P R_D_I RaKaP RfKfP RWDhDhI RWViVi[ RaKlKlP R[RXVRX',
	    'PoRE[E RfGlG R^QjQ R^TjT R^WjW RUWZW RUKUZ RTNZNZW R^M^W R^GbGaL RiGiL R^MjMjW RgXl[ RaY\\[ RVGRS RcD]D]L RkDeDeK',
	    'Po]DlD RRE[E R_HlH R`NjN R`QjQ RUWZW RUJUZ RTNZMZW R_T_[ R`K`Q RcFcJ ReKeX RhFhJ R`KjKjQ RaXhWiY RVGRR R_TkTk[h[ R]D]Q[Y',
	    'PoREZE RaKeK RZP_P RfPkP RZSlS RUJUY RaFaPdPc[ RbCbFeFeP RgGiL R[G]L R_M_Q RjLkQ RcShXlY RjGgP R\\D\\I R_H[P RhDgI RcS^XZZ RVESQ RTNYMYWTW',
	    'PoRF[F RbFlF RROlO RYZgZ RWCWM RZUZ[ RgCgM RXVgVg[ RWGZK RdOhS R^I`L R^D`G RhHlL RgEiJ RXWSY ReIbL R\\RUX RVHRL R`D]H R`I\\M RWRfRlT RWEUJ RfEdK R\\NWSRU',
	    'Po]DkD RRE[E R^JbJ RfJiJ RfMjM R^MbM R^RkR R^TjT R^WjW RUWZW R^ZlZ RUKUZ RTNZNZW R\\G\\J R_P_[ RdDdN ReOeZ R\\GlGlJ R_P[T RVGRS',
	    'Po\\EbE RREZE RcGlG R\\IaI R\\MaM RUJUZ R]T]X R_E_Q RgCgG RhWl[ RfWb[ RePiY RjJkM RiPeY ReIcN Ra[][ ReNgW RiNfW RTNYMYWTW RVESQ R[TZZ R\\E\\QbQbVa[',
	    'PoREZE R^QeQ R]UkU RUXYX Ra[d[ RUJUZ RTNYMYX R\\L\\O RdUd[ R\\LlLlO RiWlZ RiRlU R_GbJ RhGkJ RlEjG R_W\\Z RcN_Q R]E]G RcEbG RfEeH R^J\\J RgP]U R`C_I RhCgJ RVESQ',
	    'Po\\ElE RRE[E ReKlK ReVkV RUWYW R][l[ RUJUZ RTNYMYW R`U`[ RbGbQ ReSe[ RiGiQ R^KbKcN RgMdP RhJkP RhIfO RaJ^P RcG^H RkGeH RVESR R\\E\\S[Z',
	    'PoaEkE RSE^E RRH_H RaIjI RTM]M RaMiM RTO]O RaOjO RZQ]Q RSSlS RXZiZ RTJTQ RYUY[ RTJ^J^Q RWWiWi[ RaGjGjI RbIbQlPlQ R[E[H R[SXWRY RaCaG RaKeKaK RgKjK RdKgK RhKfK RVEVH',
	    'PoREYE ReFlF R[FcF RZLcL ReNkN ReQkQ R\\RbR ReTjT R\\VbV ReWkW RUXYX Re[l[ R_[b[ RUJUZ RTNYMYX R\\N\\[ R]H\\K R_C_F R\\NbNb[ ReNe[ ReCeIkIkN RaH`L RVESQ',
	    'PoREZE R]FlF RfIkI R_QlQ RTXZX RaZjZ RUKUZ RTNZNZX RbSb[ R_IcIbO RhHhO R`UjUj[ RaIdL RhIlN RbS^W RbH_N RhHfM RVESQ R]F]R[Z',
	    'PoRE[E R]ElE R`JlJ RaOkO RaQkQ R`UlU RUWZW Rf[i[ RUJUZ RTNZNZW RaLaS RaCaG RfHfS RgCgG RiTi[ RaLkLkS R[N_P RcWeY R\\I_K RiHkH R^S[Z RVGRS',
	    'Po]DkD RRE[E R^JbJ RfJjJ R^MbM RfMkM R\\RcR ReSlS ReWlW RUWZW ReZlZ RUKUZ RTNZNZW R`O`[ RdDdN RePe[ RePlPl[ R\\G\\IlGlJ R_ScW R_Q\\X RVGSS',
	    'PoREZE R\\FlF R]McM ReMkM R^QkQ R^TkT R^WkW RUXZX R^ZlZ RUKUZ RTNZNZX R]I]M R_P_[ RaCaG R]IcIcM RfOfZ ReIeM RgCgG ReIkIkM RdNgP R_O[T RVESQ',
	    'PoRFZF R\\FlF R\\IcI RfLlL R\\ScS RUXYX R[[l[ RUKU[ RTNYNYX R\\I\\S R]V][ R`C`G R`J`M R\\PaP`T RaVa[ R\\McMcP RfVf[ RgCgG R]VjVj[ RhOjR RgIeO RVGRS',
	    'PoRE[E R\\FbF ReFkF RdIlI RiMlM R^QjQ R^TjT R^WjW RUXZX RUKUZ RTNZNZX R^O^W R\\IbI`N R^OjOjW RfXl[ R]D\\G ReDeG RhCgIeM RaX[[ RWESQ R_C_I]M RhFiL',
	    'PoRE[E R]HbH ReHjH R\\KlK R^MdM R^ScS R^VcV RUWZW RUKUZ R\\K\\V R]D]H R^P^Y RaMaP R]DbDbH R\\PdPc[ ReDeH ReDjDjH RhWl[ RhXe[ RfQjY RjRfZ RfPkPhX RVGSS RfLeT RTNZN[TZ[ RcX]Y',
	    'PoUFjF RRLlL RZ[_[ R_L_[ RfQiTkY RXPVTRX',
	    'PoZCZH R[O[[ RZObS RSI`IZPRT',
	    'Pob[k[ RXCXH RYNY[ RbDbZ RYN]S RSI]I[LTS RlVk[',
	    'PoSE[E R^EhE RRK\\K Re[j[ RSNSW RWKW[ R[N[W R`S]Z RgMlMlSj[ RaEaM_W RhGgO',
	    'Po^MlM R]ZlZ RXCXH RYNY[ ReCeZ RXN]S RSI\\IZLSS',
	    'PoSE\\E RRK]K R_PhP Rd[j[ RSNSW RWKW[ R\\N\\W RbC^L R`IlIlRj[',
	    'PoSE\\E RRK]K R`OkO Ra[k[ RXKX[ R\\N\\X RaEaZ R`EkEkP RlWk[ RSNSTRY',
	    'PoSE]E RRK^K RfXkX RXKX[ R]N]X RbEb[ RlSlW RgNkQlU RbFlEgN RSNSTRY',
	    'PoSEZE RRK[K R`[k[ RSNSW RWKW[ R[N[W R`F`Z ReCeU RlVkZ RkI\\M RkIjTgT',
	    'PoRH[H R]UhU Rc[j[ RXNX[ RWN[R RUDXF RZIXNRR R^PlPjZ R_H^O R^EjEhO',
	    'PoSE[E RRK\\K RSNSW RWKW[ R[N[W RgMg[ ReElL RcF]L RcCbH R`M`S_X',
	    'PoSE\\E RRK\\K R^NlN RSNSW RWKW[ R\\N\\W RhWl[ RcU][ ReFeQiY RdPaX ReMhW ReKcU RjD^F',
	    'PoSE\\E R^FkF RRK\\K R^NkN RSNSW RWKW[ R[N[W RgWk[ RbU][ RdFeRhY RdQaX RdKfV RdLcU',
	    'PoSE[E RRK\\K R_NlN RSNSW RWKW[ R[N[W R_F_Z RlWk[ RjD_F ReY\\Z RfEfNgY',
	    'Po`MlM RXCXH RXNX[ RgMg[ RXN\\S RZKRS RSI\\IYM RjD`F`P^X',
	    'PoeNkN R\\[l[ RXCXH RXNX[ R_J_[ ReDe[ RXN\\R RZLSS RSI\\IYM',
	    'PoSE\\E R\\HlH RRK\\K Re[i[ RSNSW RWKW[ R[N[W RdCdH R`W\\[ RbNjNjUi[ RbHbN`W',
	    'PoSE[E RRK\\K RhLkL RSNSW RWKW[ R[N[W RaDhDhL RkIkL R`PbTfXl[ R^OjOhTcX\\[ R`D`L',
	    'Po^RlR RXNX[ R^I^T ReCe[ R^IlIlT RXN\\R RVDXF R[JRR RSH[HYM',
	    'Po]OlO R]ZlZ RXNX[ ReFeZ RXN\\R RVDYF RSH[HZKRS RjD^F',
	    'Po^GkG R^LjL RXNX[ RdCd[ RXO[S RUDXF RYMRT RRI[IWQ RkYhY R]RlRkY',
	    'PoSE[E R^ElE RRK\\K RaYkY RSNSX RWKW[ R[N[W RbLb[ RaOkNk[ RbJ]U RcE`R',
	    'Po]HlH RaYkY RXCXH RXNX[ RaNa[ R`PkPk[ RXN\\R R`Q\\U RZLRS RSI[IYM RbJ^T RcCaQ',
	    'PoSE[E R_IkI RRK\\K R]OlO RSNSW RWKW[ R[N[W RgWl[ RcU][ ReCePgX R`E^M ReKcU',
	    'PofNkN R_ZkZ RXMX[ R_Q_[ R_QkQk[ RXN\\R RUDXF RZKRR RcJ^N RSH[HYM RdEaM R^ElEkN',
	    'PoSE[E R]IlI RRK\\K RSNSW RWKW[ R[N[W RfVk[ RgDjF RgUa[ RcOgX RiOeW RaP]Y RbCbLfV RbM_W RbI`U',
	    'PoSEZE RaJlJ RRK[K Re[i[ RSNSW RWKW[ RZNZW R`H`[ RiCi[ RcNeT RaC]L',
	    'PoSE[E RRK[K Rb[i[ RSNSW RWKW[ R[N[W RbIb[ ReR\\Z RjWi[ RaDgF RjEeR RjMlT R_M]U',
	    'Po`LjL R`SjS R\\[l[ RXCXH RXNX[ R`E`Z R`EjEjZ RXN\\R RZLRS RSI\\IYM',
	    'PoSE[E RRK\\K R_MkM R]ZfZ RSNSW RWKW[ R[N[W R_F_V RlVkZ ReU]V RfEfNhY RiD_E',
	    'PoaEkE RSE]E R`KlK RRK^K RXKX[ R]N]X RfKf[ RkNkX RSNSTRY RbNbS`Y',
	    'Po^HkH R]OlO RXNX[ RdCd[ RXN\\R RVDYF RgTlY RaT\\Y RSH[HZLRS RdMgT RdM`V',
	    'PoSE\\E R`IlI RRK]K RdNkN RdSlS RXKX[ R]N]X RdHd[ RbD^M RSNSTRY',
	    'PoSE[E R^IkI RRK\\K R]OlO R`ZiZ R]ZfZ RSNSW RWKW[ R[N[W RiUl[ RdCdO`Z',
	    'PoSE\\E R]IlI RRK\\K R_ZjZ RSNSW RWKW[ R\\N\\W R_Q_[ ReCeQ R_QjQk[',
	    'Po^NjN Rf[l[ RXCXH RXNX[ R^E^N RgNg[ R^EjEjN RlWl[ RXN\\R RSH\\HZLRS RaO`W\\[',
	    'Po^NlN R^SlS RXCXH RXNX[ R^H^T ReCe[ R^HlHlT RXN\\R RSH[HZLRS',
	    'PoVGhG RTKjK RVOhO RRSlS R[[_[ RTHTK RVDVG R_C_K R_S_[ RhDhG RjHjK RgWjZ RYUTZ ReVhW',
	    'PoSE[E R_JhJ RRK\\K R_UgU Rh[l[ RSNSW RWKW[ R[N[W R_N_W R_NgNgU R^ElEl[',
	    'PoYG_G RbLlL RVOiO RSTlT RZ[_[ RTETM RYCYL R_T_[ RbCbL RhXk[ RXVTZ RlJkL RjEaG R\\KRM R_KTM',
	    'Po_IlI RWCWH RXNX[ RXN\\R RZLRS RSI[IYM RaE\\N R`O\\V RiNlW RdHd[a[',
	    'Po_HkH RXNX[ R`L`[ RhVlZ RXN\\R RVDYF RSH[HZKRS RePiY RaC^K RjPfQ ReX_Z RdKhW RiJ_L',
	    'Po_[c[ Rh[l[ RXMX[ R]EcEc[ RfElEl[ RWN[R RUDWF RcO_S RlOhS RYKRR R^I`N RfIhN RRH[HXM',
	    'Po^IlI R^NkN R]TlT RXCXH RXNX[ RdId[ RXN\\R RZKRS R`DbH RSI\\IYM RiChH',
	    'Po^KjK R]OkO R\\SlS RWMW[ R^E^K R^EjEjK RWN[R RUDWF RYLRR RdRfWhYl[ RRHZHWO RcV`Y\\[ RdOcW',
	    'PoSEZE RRK[K Rf[l[ RSNSW RVKV[ RZNZW RfDf[ RlXl[ RgPkT R`V\\[ R]G^L RjGhL RaQ\\S RaDaR`W',
	    'PoRElE RTMjM RVQhQ RRTlT R[[_[ RTHTM R[E[M R_T_[ RcEcM RTHjHjM ReVlZ RXWRZ',
	    'PoSE[E R_IjI RRK\\K R]OlO RSNSW RWKW[ R[N[W RdCd[ RgTlX RaT\\Y RdMhU RdMaU R`D^M',
	    'Po]KlK RfUiU Rb[k[ RWNW[ RbNbZ RbNjNjU RWN[R RUDXF RlWk[ RYKRR RSH[HXM R`FgFeJ R`C]J^S\\Z',
	    'PoSE[E RRK\\K R`LiL R_ZjZ RSNSW RWKW[ R[N[W R_Q_[ R_QjQj[ ReElL RcF\\L RcCaH',
	    'Po]EkE R^MjM R]SkS R[ZlZ RXNX[ RdNdZ RWN[R RVDXF RgHjK RYLRR RcEaI]M RSH[HXM',
	    'PoZNeN RUSjS RZ[_[ R_S_[ ReUjZ RXUTZ R`DcJgNlP RYDSJXL RWE^E\\IWORP RcEkEeL RVG\\I',
	    'Po]HlH R]NkN R^ZkZ RXNX[ R^R^[ RdCdN R^RkRk[ RXN\\R RVDXF RZKRS RRH[HYM',
	    'PoeFlF RSH\\H RXNX[ R_K_U ReCeK R_KkKkU RXN\\R RVDYF R[JSR RfXlZ ReNdUaY\\[',
	    'Po^IkI R[NlN R^ZjZ RXNX[ R^R^[ RdCdN R^RjRj[ RXN[R RVDYF RSH[HZKRR R_D]K',
	    'Po^QjQ Rf[l[ RXMX[ R^I^Q RgQg[ R^IjIjQ RlXl[ RWN[R RUDWF R`W\\[ RYLRR R`CaG RRHZHWO RiCgI RbQ`X',
	    'PoSEZE R]HlH RRK[K R^LjL R_RjR RSNSW RWKW[ RZNZW R]O]R R_DjDjK R]OlOkR R`TdXj[ RiSeXbZ\\[',
	    'Po^EkE RaIkI R^NlN RXNX[ RbNb[ RXN[Q RhVlZ RVDYF RkQgT RSH[HYLRR RfPiX RfYa[ ReNhV R^E^Q\\Z',
	    'Po\\IlI R\\QdQ RWMW[ RbKbX RWN[R RUDWF RhDkF RgUb[ RXLRR RjMeX RRHZHWO RlVk[i[ RfCfMhZ R^K^S]Y',
	    'Po^KjK R\\ZlZ RWMW[ R\\E\\[ ReGeW R\\ElEl[ RWNZR RUCVF RdLiQ RbP^T RXLRR RRHZHWO RdJaR',
	    'Po^MkM RXMX[ RdHd[ R]HkHkM RWN[R RUDWF RhCgH R_X[Y RdR]Y RYLRR R_CaG RRHZHWO RkXgX R^M]RlRkX',
	    'Po]GkG R^JkJ R\\NlN R\\RlR Re[h[ RWCWH RXNX[ RiNi[ RaUdX RXN[Q R`T[Y RZLRS RSI[IYM RbN^V RcEaR',
	    'Po^JkJ Rh[l[ RXMX[ R]O][ R^D^J R^DkDkJ R]OlOl[ RWN[R RUDWF RdQjV RdS`W RYLRR RRHZHXM ReKdS',
	    'PoSEZE RRK[K R`LhL R^PiP R_WhW Rf[j[ RSNSW RWKW[ RZNZW R_S_Y RcLcW RhShX R`I_N R`C^IlHlTj[',
	    'PoSE[E R]GlG RRK[K R`LiL R`QiQ R\\UlU RSNSW RWKW[ R[N[W R`C`U RiCiU RgWl[ RaW^Z',
	    'PoSE[E RaKjK RRK\\K RbVhV R^YlY RSNSW RWKW[ R[N[W R^E^[ RbPbV ReGeO RbPhPiV R^ElEl[',
	    'PoSEZE R^IjI RRK[K R^NjN R\\QlQ RSNSW RWKW[ R[N[W R^D^N RdDd[ R^DjDjN ReSlY RcR\\Y',
	    'Po\\GlG R\\TlT RWMW[ RdQd[ RWNZR RVDWF R_LbO RgLkP RXLRR RcCeG RhIeP RRHZHWO R`I]O',
	    'PoSEZE R^FjF R\\JkJ RRKZK RSNSW RWKW[ RZNZW RdCdJ RfJfOkNkO RaL]P RbP]V R`SeXl[ R`SiSeX][',
	    'PoSEZE R_JhJ RRK[K R\\NlN Ra[d[ RSNSW RWKW[ RZNZW RdNd[ R^Q`S RfTkY RkQhS RdOgV RbV\\Y R`C_L R`FiFhN',
	    'PoSElE RZMdM RVPiP RVRiR RRUlU RZ[_[ RVHVO RZJZM R_C_F R_U_[ RZJdJdM RVHiHiO ReWkZ RYWRZ',
	    'PoSF]F R`FkF RVPiP RSTlT RZ[_[ RYCYN R_T_[ ReCeN RgWkZ RXF[I]K ReFlM RXVTZ RYFRM ReF_M',
	    'PoaJiJ R`NjN RaWhW Rh[l[ RXMX[ RaRaX ReGeN RaRhRhW R^ElEl[ RWN[R RUDXF RYKRR RSH[HWO R^E]Q\\X',
	    'Po\\GkG R^NiN R^RiR R^ViV R[ZlZ RWNW[ R^J^Z R^JiJiZ RWN[R RVDXF RYLRR RRHZHWO RcCbJ',
	    'Po_IiI R\\MlM R`[d[ RWCWH RXNX[ RdMd[ R^DiDiM RfTkY R]P`S RXN[R RkPhS RZLRS RdOgW RSI[IYM RbV[X',
	    'Po^MkM R^RkR R\\VlV RWCWH RXNX[ R^I^R RcDdG RdId[ R^IkIkR RXN[R RZLRS R]D_H RSI[IYM RjDhI',
	    'Po]KjK R]PjP R[UlU RXMX[ R]F]P RfQf[ R]FjFjP RWN[R RUDWF RcP^T RYLRR RbCbF RRHZHWO RdKaR RdGcP',
	    'Po^GhG R]LlL R`UjU R`ZjZ RWMW[ RaPa[ R_QjQj[ RWNZR RVDXF RjGbO RXLRR RRHZHWO RcCdLaP\\R',
	    'Po]IkI R^PjP R^TjT R[WlW RWMW[ R\\E\\H R^L^T RdGd[ R^LjLjT R\\DkDkG RWN[R RUDWF RYLRR RRHZHWO',
	    'Po\\HlH RfRkR R]VkV R]ZkZ RWMW[ R]L][ RbHbQ RgGgR R]LkLk[ RWNZR RUCWF RXLRR R`DaF RRHZHWO RhCfI',
	    'PoSEZE R\\FcF R\\IbI RRKZK R\\SkS RSNSW RWKW[ RZNZW R`C`P RdEkEkN RdRfWhYl[ RcV`Y[[ RdPbW RjOhO RgEfN RcL[M',
	    'Po\\ElE RSEZE RRK[K RfMkM R]PkP R]UkU R[ZlZ RSNSW RWKW[ RZNZW R]I]P RbEbL RdPdZ RfEfL R]IkIkP R`M^M',
	    'PoSEZE R\\HlH RRKZK RSNSW RWKW[ RZNZW R\\O`S RdOhS R_CaG RhJeP RjNeZ R`J]P RbN]Z RhCfG RbVcZ[Z RjUkZdZ',
	    'PodIjI RbWhW Ri[l[ RXCXH RXNX[ R]N][ RbRbY RdIdN RbRhRhW R`EjEjN R`E_NlNl[ RXN\\R RZKRS RSI[IYM',
	    'PodFlF R_NjN R_RjR R_VjV RXCXH RXNX[ R_J_V RdCdJ R_JjJjV RgXkY RXN\\R RZLRS RSI\\IYM R`Y\\[',
	    'Po]ElE R_MjM R^UlU R^ZlZ RXCXH RXNX[ R^P^[ R_H_M RdPdZ R_HjHjM R^PlPl[ RXN\\R RZLRS RSI[IYM',
	    'Po\\KkK R\\OlO RXMX[ RWN[R RUDWF R^VZ[ RYLRR R]F^I R\\EcEcI R`SeXl[ RSHZHWO R`Q\\Y RiFgK R`SjSeX^[ RaK_V RjCbD',
	    'PoSE[E R_IkI RRK[K R_MkM RbViV R`WfW RSNSW RWKW[ R[N[W R^P^[ R_E_M ReEeV R_EkEkM RhSiX R^PlPl[h[',
	    'Po_JhJ R\\LlL RWMW[ RWN[R RUDWF RhRkY RYLRR RRHZHWO RcN]Q RdR]U ReTbW\\Z R`C_I RkOdQfWe[a[ R`EhEgL',
	    'PoSEZE R_HjH RRK[K R_LjL R]OlO RSNSW RWKW[ R[N[W R_D_L R_DjDjL RaP\\U RcT]Y RfVb[ RgRdY Rg[j[g[ R_SkRj[',
	    'PoSE[E R_HjH RRK[K R_LjL R]OlO RdTjT Rc[l[ RSNSW RWKW[ R[N[W R_D_L RdOdZ R_DjDjL R_UaY R_R]Z',
	    'PoSEZE R]JlJ RRK[K R_QjQ R_TkT R\\XlX RSNSW RWKW[ R[N[W R_T_X R_L_Q RfQf[ R_LjLjQ R^FjFjJ RcCaJ',
	    'PoSEZE R\\GkG RRK[K R`KhK R`OhO R\\RlR RSNSW RWKW[ R[N[W R`C`O RdOd[ RhChO RdRkY RdR]Y[Y',
	    'Po]JkJ R]OkO R`ZhZ RXMX[ R]E]O R`R`Z RdEdO R]EkEkO RWN[R RcRfU RUDWF RhWhZ RXLRR RiSlY RRHZHWO R\\S[Y',
	    'PoSEZE R]FkF RRK[K RgXjX RSNSW RWKW[ RZNZW R]K]N R_P_X RdCdF RdMd[ R_PjPkX R]KlKlN RaGaK RhHhK',
	    'Po\\FaF R^RjR R^VjV Rf[j[ RWNW[ R^O^[ R^OjOj[ RfCfLlKkL RUDWF RWNZR RXLRR RSHZHVP RkFfG RaCaL\\K',
	    'Po]HkH R]MkM R\\SlS RXMX[ RaVb[ RdHdS RWNZQ RUDWF RYLRR RfVf[ R`D`G RiVl[ RRHZHWO R]V[[ RhCfH',
	    'PoSEZE R_HkH RRKZK RaNjN RaPjP RaSjS R]VlV RSNSW RWKW[ RZNZW R]K]V R_C_H RaKaS RaKjKjS RgXlZ RbX\\[ RiD^F',
	    'PoWPgP RSTkT RZ[_[ R_T_[ RXG[J RfVjZ RXVTZ RkEhH RUETH R]E[G RaE`H RdHlK RdI_K RWIRK RSMSQ RYCWI ReCdI RSMkMkQ',
	    'PoSEZE R^HjH RRK[K R^LjL RfPlP RSNSW RWKW[ RZNZW R^D^L R^DjDjL RaM^P RcP]V R`SdXk[ RdDeLfP R`SiSeWaY[[',
	    'Po[FlF R^MiM R`XgX RXMX[ R\\P\\[ R^I^M R`S`X R`SgSgX R^IiIiM RWNZR RUDWF RYLRR RcCdF RRHZHWO R\\PkPk[h[',
	    'Po]GlG R_NlN R`QjQ R`ZjZ RXNX[ R`T`[ ReHeT R`JjJjQ R`TjTk[ RWN[R RVDXF RcDfF RYLRR RRH[HXM R]G]Q\\Y',
	    'Po\\EbE ReHlH RcOiO Rh[l[ RWMW[ ReFeK RcThTh[ R_KkKkN RlXl[ RUCVF RVNYQ RWMRR RRHYHVO RcTbZ ReLeRkQkR R\\E\\R[Y R_K_S^Z RiD_E RgNaO',
	    'Po^ElE RSEZE R^IkI RRK[K R^MkM Rf[k[ RSNSW RWKW[ R[N[W RaUaY ReEeQ RhTjV ReUeX R]T\\Z R^E^QlRk[',
	    'Po]HkH R]LkL R_[c[ Rh[l[ RWNW[ R]D]L R\\OcOc[ R]DkDkL ReOlOl[ R\\Q_T RWN[R RVDXF RfQiT RYLRR RSHZHXM RcT[X RlTdX',
	    'Po\\FlF R^MkM R^PkP R\\TlT Rd[g[ RXNX[ R^I^R RdCdQ RhRh[ R^IkIkQ R`VbY RXN\\R RVDXF RZKRS RgDjD RSH[HYM',
	    'Po\\KlK R^[b[ Rh[l[ RXMX[ R\\D\\K RaDaK R\\NcNc[ RgDgK ReNlNl[ R\\DlDlK R\\Q_T RWN[R RUDWF RfQiT RYLRR RSH[HWO RbU[X RlTdX',
	    'Po]GkG R_JhJ R_MhM R]QlQ R_VhV R\\ZlZ RWNW[ RdSdZ RWN[Q RVDXF RhCgQkV R_C`Q\\V RYLRR RRHZHWO',
	    'Po[FcF RZHdH R_LcL RiOkO R[PbP RYP_P RVRiR RRUlU R[[_[ RVIVQ R[K[P R[CZG R_E_O R_U_[ RfDfQ RfDkDkO RVCRG RWGRL ReWlZ RYWRZ',
	    'PoSEZE R\\ElE R]IkI RRK[K R^OjO R\\SlS R^ZkZ RSNSW RWKW[ R[N[W R^V^[ R^K^O RdCdI R^KjKjO R^VkVk[ R`Q`S RhPgS',
	    'PoSEZE RRKZK R]QlQ RSNSW RVKV[ RZNZW R_TbW R\\G^J ReGhJ RlXk[ RgOiP RgZk[ RiGfM R_D]H R`G]N RhCfH RiTdY`[ RdCdJfX R]Q\\Z RaKbN[N RjKkNeM',
	    'PoSEZE R]HkH RRK[K R]LkL R]PlP R\\UlU RSNSW RWKW[ RZNZW R]D]L RaMaU RdDdL RhMhU R]DkDkL RhXk[ R`Y\\[',
	    'PoSEZE R]IcI RfIlI RRK[K R^OkO R^SkS R\\WlW RSNSW RWKW[ R[N[W R]D]I R^K^S R]DcDcI RdKd[ RfDfI R^KkKkS RfDlDlI',
	    'Po]DlD RSEZE RRK[K RbKfK R^QkQ R^TkT R\\WlW RSNSW RWKW[ R[N[W R]H]L R^N^T RbDbK RdTd[ RfDfK R^NkNkT R]HkHkL',
	    'Po]QkQ R^WjW R^ZjZ RWMW[ R]K]Q R^S^[ RdKdQ R^SjSj[ R]KkKkQ RWN[R RUDWF RdChG RXLRR R`M`O R`IgHlI RgMgO RRHZHWO RdC_H[I',
	    'Po`HlH Ra[l[ RWNW[ RhOhW RVNZR RVDWF RgOlT RbCcG ReHaM RdP`Q RfMbQ R[M]M]XZZ RXMRR R[E]I RSHZHVO RiCgH RkKgOcU_V RgXdY R]MZM RgMhU',
	    'PoSEZE R]IkI RRK[K R]LkL R\\OlO R^UjU R\\[l[ RSNSW RWKW[ RZNZW R]E]L RbCbL RfCfL R]EkEkL R`WaY RgWgY R^QjQjU R^Q_U',
	    'PoSEZE R^IkI RRKZK R^LkL R]OlO R_QjQ R]TlT RSNSW RWKW[ RZNZW R^F^L R`T`Z RbCbL RfCfL R^FkFkL ReUhYl[ RkVgX RfY^Z R]O]T\\Z',
	    'PoSEZE R\\ElE RRKZK Rh[k[ RSNSW RVKV[ RZNZW R]J][ RdEd[ R]JkJk[ RhFlJ R_MbO^Q RbS`W RiLgP R_TbW^Y RfMiOeQ RiSgX RfTiWeY R`F\\J',
	    'PoSEZE R\\ElE R^HjH RRKZK R^MiM R]OkO R\\RlR RcUlU R]YbY Rf[i[ RSNSW RWKW[ RZNZW R]T]Z R]TbTbY RcMcO RdCdH RjSj[ RdWfY R\\JkJkM',
	    'Po]DlD R_HlH R`NjN R`QjQ RXMX[ R_T_[ R`K`Q RcFcJ ReKeX RhFhJ R`KjKjQ RWN[R RUDWF RaXiWiY RYLRR RRHZHWO R_TkTk[h[ R]D]R\\Y',
	    'PoSEZE R\\FlF RRKZK ReLkL R\\NlN R]QkQ R\\SlS RSNSW RWKW[ RZNZW R]KbKaS R]HbHbL RdCdF ReHfLgS ReHkHkL ReUhYl[ RcT\\X RkUfW R`V`Z R]H]L ReZ][',
	    'PoSEYE RRKZK RhPkP R]VjV RSNSW RVKV[ RZNZW R]R][ R]K`K`O RaRa[ RbKeKeO RfRf[ RhKhO RhKkKkO RdChH R_IgHlI RcC_H[I R`K]K]P`P]O ReKbKbPePbO R]RjRj[h[',
	    'PocEkE R[HaH RdLkL R[LaL RZObO RdPkP RdSkS R^TbT R_ZlZ RWMW[ R[D[L R[DaDaL RdHdS RhEfH RdHkHkS RUCVF RVNYQ RiUkX ReUcX RWMRR RRHYHVO R[RZ[ R_O^YeZ',
	    'Po\\FbF ReFkF RdIlI RiMlM R\\QjQ R\\TjT R\\WjW RWNW[ R\\O\\W R\\OjOjW RUCVF RVNZR RWMRR RfXl[ RRHYHVP R\\D[G R_C^I\\M ReDdG RhCgIdM R[IaIaM_M RaX[[ RhFiL',
	    'PocElE RZIbI RdMjM RdRjR RZSbS RdVjV RVMV[ R^C^N RdIdV RdIjIjV RUCVF RiXl[ R^UbX RVNYQ R]IbM RZD[G R]VYZ ReXc[ RVNRR RaD`G R^IYN RRHXHUP RhEfI R^P]W',
	    'Po[SgS RVSdS Rf[l[ RRIR[ RRIlIl[ RaMiT R^CZS',
	    'PoVEbE RTNjN RVWgW Rf[l[ RRQR[ RTITM RRE_E_W RTIjIjM RRQlQl[ RdTiX RkDdD_E',
	    'PoTIjI RTNjN RVWgW Rf[l[ RRQR[ RTETM R_E_W RTEjEjM RRQlQl[ RdThX',
	    'PoRFlF RVNhN RWWfW Rf[j[ RTRT[ RVHVN R_C_F RhHhN RTRjRj[ RcTgX RbHYM RZIcK R]N[W',
	    'Po^FlF RTPjP R\\WfW RXWbW Rf[k[ RRSR[ RTITO R^C^H RTIjIiO RRSkSk[ RbTgX R]RXW RYJeN RdJ^MXN',
	    'PoYIeI RWQgQ RXXeX Re[i[ RTST[ RWKWP R_G_I RhKhP RTSiSi[ RbUfY R`DkJ R^QZX R]ERJ RZLdN RcK^MYN',
	    'PoTGZG RRMlM R_F_[ R`NhVlX R^NVVRX R\\FWF RhD\\F',
	    'PoRIlI Rc[j[ RTFZE_E_P RcQc[ RkXj[ RaIdMlP R^IZMRQ RZVWYS[ R[RYX RgC]E',
	    'PoSIkI Rb[g[ RUFZE_E_O RaIdLlP R^IZMRP R[RYWWZS[ RWQcQbUiTh[ RhC^E',
	    'PoRK_K RYEY[ RYN]S RhSl[ R^DRF RYLUSRV ReD_ZjX',
	    'PoRIlI RVE_E`N R`IdMlQ R_PaUdXj[ R_HZMRQ R_P]UZXS[ RgD_E',
	    'PoRIlI Rd[k[ RUE`E`P RZRdRd[ RlXk[ RaIcMlP R^IZMRQ RXYR[ RZRYY RgD_E',
	    'PoRK\\K R^OlO R`[e[ RYEY[ ReKe[ RXN\\R R_FkEfJ RXKST R\\DRE',
	    'PoRK\\K Rd[k[ RYEY[ RdEd[ RXN\\R RlWk[ RXKTT RjD^F R\\DRE RgN]P RjN`O',
	    'Po^EkE RRK\\K R]NlN RXEX[ ReEe[ RXL[P RXKST R[DRE',
	    'PoRK\\K Rh[l[ RXEX[ R]IhIi[ RlWl[ RXN\\R R_W[[ R\\OdS RXKSU RXHWO R[DRE RbCbM`V',
	    'PoRK\\K R_XlX RYEY[ R_K_[ ReDeX RlKlZ RXN\\R RXJST R\\DRE',
	    'PoRHlH RROlO RURiR RUE`E_[ RUKiKiR R`RcUlY R^R[URY RhC`D',
	    'PoRIlI RVQ]Q RRUlU R_Q_[ RVE_E_O RaIcLlP R]I[LRP RgC^E RfO\\Q',
	    'PoRK]K RYEY[ RZN[Q RkIhP RXKSU R`I^P R[DRE ReCeNbS`W][ ReOgTiWk[',
	    'Po\\LlL RdEd[ RWCWSZU RgRlW R_S[X RcKgS R[HYM RcK_T RTISO RWNVW RiD\\F',
	    'PoSK\\K R_RlR RYEY[ R_I_T ReCe[ R_IlIlT RXL\\P RXKTT R\\DSF',
	    'PoRK\\K Rc[h[ RXEX[ RWL[P RfFjN RXKST RbF^M RbO`X R[DRE R]NiNiTh[',
	    'PoRK[K Rc[k[ RXEX[ RcFc[ RkWk[ RWL[P RWKRU RhD\\H R[DRE RjK_M RhR^S RjQ`S',
	    'Po^FjF RRK[K R\\NlN RcYjY RXDX[ RWM[P RgSk[ RWKRV RcN^X R[DSE RdX]Y',
	    'PoRK]K RXEX[ RhCh[ RXLZO\\Q R`FdG R_McO RXKST R[DRE RkS^U',
	    'PoRK\\K RaSdS RYEY[ ReCeR RXN\\R RiHlO RXJSU RiQhUdX^[ R_H^O R[DSF',
	    'Po\\HlH RSK\\K RXEX[ RdCdN RXN\\R R_NaTeXl[ RXJST R]OjNhScW[[ R\\DRE',
	    'Po\\HlH RRK]K Rh[l[ RYEY[ RdCdH R`MhMiZ RXN]S RlVlZ RXKSU R\\DRE R`M`S_X',
	    'PoRK[K R]LcL ReZlZ RXEX[ R]D]Z RfDfZ RlWlZ RWM[Q RWKRV RkKeM RcY[Z RZDRE RWHVO',
	    'PoRK\\K R^NlN RYEY[ R^F^Z RXN\\R RXKSU RfX\\Z R[DRF RfEfOhY RjD^F RlWk[iZ',
	    'PoRK\\K R]NlN RYEY[ RhWl[ RXN\\R RbU\\[ RdEeQiY RXKTT RdP`X RdLgW R\\DRE RdKcT RjD^F',
	    'PoRK\\K Ra[i[ RXEX[ RbHb[ RiWi[ RWM[Q ReQ\\Y RiMlS R`DdE RjFeQ RWKRV RWIUR R]M\\T R[DRE',
	    'Po]IlI RRK]K R^ZkZ RYEY[ R^Q^[ RdCdQ R^QkQk[ RXN\\R RXKTT R\\DRE',
	    'PoRK\\K R^LkL R]RlR RYEY[ RdCd[ RXL\\P RiFkJ RXJSU R`E^J R\\DRF',
	    'PoRK[K RXDX[ RdCdN RWM[R R`ObTgYl[ R^HlHkL RWKRV R^OjOhScX][ R[DRE R^H^Q\\Y',
	    'Po_JkJ RRK[K Rb[k[ RXEX[ RbMb[ R_EkEkJ RWM[R RlWk[ RWKRV RjPbT R[DRE R_E^Q]Y',
	    'PoRK\\K R^KkK RYEY[ ReEe[ RXN\\R RhRlV R`S\\W RdJiT RXKST RdJ_T RjD^F R\\DRE',
	    'PoRK\\K R^QlQ R^YlY RYEY[ R^I^[ ReCeY R^IlIlZ RXN\\R RXJST R\\DRE',
	    'PoRK\\K R_LiL R_SiS R\\ZlZ RXEX[ R_E_Z R_EiEiZ RWL[P RXKSU R\\DSE',
	    'Po\\ElE RRK\\K R\\ZlZ RXEX[ RdIdX RXN\\R RfKlP RdI]P RXKST ReFcK R\\DRE',
	    'Po]JlJ RRK\\K RYEY[ ReUk[ RXN\\R RfDiG R^V[Z RfU`[ RaCaNfW RiNeX RXJST RaP]Y RaL^W RaIcQ R[DSF RaJ`U',
	    'PoRK[K R_LhL RfYjY RXEX[ RbQb[ R]QjQjX RWL[P RdDlL RcCbF[L RWKRU R[DRE',
	    'Po]HlH RSK\\K R]NkN RXEX[ RdCd[ RXN\\R RgSlW RbR\\X RdMgS RXKST RdMbR R[DSF',
	    'Po^ElE RRK\\K R]QlQ RYEY[ ReEe[ RXN\\R RiHkN RXJST RaH^N R\\DRE',
	    'PodIlI RRK]K R_ZjZ RXEX[ R_Q_[ RdCdQ R_QjQj[ RXM\\R RXKSV R\\DRE',
	    'PoTFjF RVJiJ RRMlM RVThT R_Q_[ RbKhRlS RVRRS R[KTS R`TcWjZ R_DZM R^T[WTZ RcPYQ',
	    'PoSK[K R\\QlQ RXEX[ R_H_Q R_HjHjQ RfVk[ RWKZQ RdCdRhX RWKRV RcU_Y\\[ R[DSE RdMcV RXNVN',
	    'Po_HlH RRK[K RcMlM RcSlS RXEX[ RcHc[ RWL\\Q RWKRU RaC]M R[DRE',
	    'Po^IkI RRK[K R\\PlP RXEX[ RYN[Q RXKSU R_E]M R[DRE RdCdPbU]Z RdPgUk[',
	    'PoRK\\K R^MlM R\\ZfZ RXEX[ R^F^V RXN\\R RXKSU RdU[V R[DRF ReEeLgW RiD^F RlVkZiZ',
	    'Po\\IkI RRK\\K RiYlY RYEY[ RdCd[ RiLiY RlVlY RXN\\R RgDkF RXKSU R[DRE R_L_Q^W',
	    'Po]ElE RRK\\K R_SjS R\\[l[ RYEY[ R_E_Z R_LjLjS RXN\\R RXKSU R\\DRE',
	    'PoRK\\K RYEY[ ReCe[ RXN\\R ReO]X RXJSU RkXgX R\\DRF R]PlPkX R^I]Q RkG^H',
	    'PoRK\\K RaMkM R]MhM R_ZjZ RXEX[ R_Q_[ R_QjQj[ RWM[P RjJlN RgHjJ R`J]M RXKSU RcC`J R[DRE',
	    'PoRK]K R_PkP RXEX[ R_E_P R_EkEkP RXM\\Q RiTl[ RXKSU RaT^Z R\\DRE',
	    'Po_IlI RRK\\K Ra[e[ RYEY[ ReHe[ RXN\\R RXKSU R`E]M RiNlW RaC^L R`N^V R[DRF',
	    'Po`IiI RSK[K RbUhU Rh[l[ RXEX[ R^E^[ RbMbV RbMhMhU R^ElEl[ RWM[Q RWKRV RZDRE',
	    'Po^GkG RRK[K R]LlL R]QlQ Rd[h[ RXEX[ ReCeL RhLh[ R_TcW RWMZP RWKRV RZDSE',
	    'PoSK\\K R^LlL R_ZkZ RYEY[ R_R_[ RfEfR R_RkRk[ RXN\\R RXJST RjD^F R\\DRF',
	    'PoRK[K R`LhL R_ZiZ RXDX[ R_Q_[ R_QiQj[ RWMZP ReFlM RdCfH RbG\\M RWKRV RcCaI RZDRE RWHVO',
	    'PoRK\\K R_LjL R^YlY RXDX[ R^D^[ R^DlDl[ RWM[Q ReFeNgQiW RXKSU R[DSE ReJcS',
	    'PoRSlS RUQ`P_[ R^FkFhI R^E[H RbIkM RTEZF R`ScWl[ RcFaJ_LZN R]SZWR[ RZKRL RgO^P',
	    'Po]ElE RRK[K R^MjM R^SkS R\\ZlZ RXDX[ RdNdZ RWMZP RhJkM RgHjK RcEbI]M RWKRV RZDRE RWHWO',
	    'Po\\HlH RRK\\K R]NkN R^ZjZ RXEX[ R^R^[ RdCdN R^RjRj[ RYNZR RWKRU R[DRE',
	    'PoSK[K RaOlO RaZlZ RXEX[ R_H_[ RgFgZ RWM[Q R^I[M RWKRV RaC]K RkEaG RZDRE',
	    'PoRK[K Rc[k[ RXEX[ R]G]J RcLc[ R]GkGlJ RWL\\Q RlXk[ RcDdG RXKSU RiK^M R[DRE RgR]S RjQ_S RlQbR',
	    'PoRK\\K RYEY[ RXN\\R R`HfK RdC_I RXKSU RcQ]T R\\DRE RaFkFfL^O RcQmQgWbZ][ RaSgV',
	    'Po^IjI RRK[K R\\OkO RXEX[ RdCd[ RWMZP RfRlX RaR[Y RdMfS RWKRU RdMaS R_D]L RZDRE',
	    'Po^JlJ RSK\\K RYEY[ R^E^J ReCeJ RlElJ RXL\\P RbN]S RXKST RbSgW^[ RcJaPlOjTeX\\[ R\\DSE',
	    'PoRK[K RWEW[ R\\H\\K R`O`Z R\\HkHkK RhVlZ RWMZP RaN\\S RjNfR ReOiX RWKRT RcG_Q RcHgV RdDaN ReY_Z RZDRE',
	    'Po\\GlG RRK[K R]PkP R]UkU Rh[k[ RXEX[ R]L][ RdCd[ R]LkLk[ RWN[R RgDkF RWKRV R[DRE',
	    'PoSKZK R\\LlL RgYjY RWEW[ R_P_Y RdNd[ R_QjQjX RVMZP RWKRU RiD]J R^EjH RZDSE RcI`O[S',
	    'PoRFlF RVLhL RSTlT RSNSQ RVHVL R_C_F RVQ[Q_Q_[ RVHhHhL RSNkNlQ RaVkZ R_T]VSZ ReP]Q',
	    'PoRK\\K R^KjK R^PjP RYEY[ R^F^Z RdCdG R^FjFjP RfUk[ RXN\\R RdPgX RXKST RkSfU R[DRF ReY[Z',
	    'PoRK\\K R_MkM R]SlS R`[d[ RYEY[ RdQd[ RdFdJ RXN\\R RjFkJ RXKSU R_E]J RiNdP R[DRE RiD_E',
	    'Po`IjI R]IgI RRK[K RXDX[ RWMZP RgKkO RfEkJ RbN[U RcC]I R`J\\O R^RcXk[ RWKRV R_RiRdW`YZ[ RZDRE',
	    'PoRK\\K Rf[l[ RXEX[ R_K_QhQg[ R_KiKiQ RlXl[ RXN\\R R`W\\[ R`F\\J RbEhDjI RXJST RaR_Y R[DRE',
	    'PoRK\\K R_MkM R^ZlZ RXEX[ R^Q^[ R_E_M R_EkEkM R^QlQl[ RWM\\R RWKRV RdMbQ R[DRE',
	    'PoSK[K R_KjK R]ZlZ RXEX[ R]E][ RdGdW R]ElEl[ RWM[Q RdKiT RWKRV RdK`U RZDRE RWHVO',
	    'Po^IkI RRK\\K R^MkM R^PkP R\\UlU RYEY[ R^E^M RdPd[ R^EkEkM RXN\\R RXKST R\\DRE',
	    'Po]ElE RRK\\K R^MkM R^RkR RYEY[ R^I^R R^IkIkR RXN\\R RXKST RcU`Y[[ R_TcXfZl[ R[DSF RdEdQcV',
	    'PoRK\\K R^MkM RgYkY RXEX[ RdHd[ R]HkHkM RWL[P RiChF RdR]Y R`CaG RWKRU R^M]RlRkY R[DRE',
	    'PoRK\\K R_LjL R]PkP R^UkU R\\[l[ RYEY[ R_D_L RdPdZ R_DjDjL RXN\\R RXJSU R[DRF',
	    'PoRK[K R_KiK R]QkQ RXEX[ RXN\\R RdCkJ RdC]J RiSkY RXKSU R_S]Y RdKd[`[ R[DRE',
	    'PoRK\\K R_PjP R_UjU Rg[j[ RYEY[ R_K_[ RdCdK R_KjKj[ RXN\\R RhElH R`F]I RXKST R[DRE',
	    'PoRK\\K R^QjQ Rf[l[ RYEY[ R^I^Q RgQg[ R^IjIjQ RlXl[ RXN\\R R_CaG RXKST RiChH R\\DRE RbQaW\\[',
	    'Po\\ElE R]KaK RRK[K RfTkT R[ZlZ RWDW[ RaEaK R]K]TaTaZ RfTfZ RfEfKkKkT RVMZQ RWKRV RZDRE RWHVP',
	    'Po_KhK RSKZK R\\TkT RWDW[ R\\O\\[ RaOa[ RfOf[ RcDhIlJ RcC\\K RVKYQ RWKRU R\\OkOk[h[ RZDRE RVHVO',
	    'Po^FkF R\\KlK RRK[K R]UkU R[ZlZ RXEX[ RdCdK RdQdZ RgKgPlOlP RWM[Q RWKRV RaK`O\\Q RWIVP R[DRE',
	    'Po_KjK RRK[K R`VhV R]YlY RXDX[ R]E][ R`P`V RdGdO R`PhPhV R]ElEl[ RWM[Q RWKRV RZDRE RWHVP',
	    'Po]GkG RRK\\LlM R_UjU R_ZjZ RXEX[ R_P_[ RaCaM RhChM R_PjPj[ RWM[Q RWKRU R[DRE',
	    'Po`JhJ RRK[K Ra[h[ RXEX[ RaUa[ RhXh[ RWL[P RdCiHlI RdC]I RjUlZ RbSeT R^MiMgR RWKRU R]U[Z R[DRE',
	    'Po]FlF RRK\\K R_NjN R]VlV Ra[e[ RYEY[ R_I_N ReTe[ ReCeG R_IjIkN RXN\\R RXJST R_RjReT R\\DRE RXGWN',
	    'Po]GkG RRK[K R]NlN R_ZjZ RXEX[ R_R_[ RdCeG R_RjRj[ RYNZQ R`H`M RiIfN RWKRV RZDRE',
	    'Po^KkK RRK\\K R^OkO R[VlV RYEY[ R^G^O RgQg[ R^GkGkO RXN\\R RdGdO`S RdCcF RXJST R\\DRE',
	    'Po\\HlH RRK\\K R`LiL R`PiP R[UlU RXEX[ R`C`U RiCiU RhWl[ RXN\\R R`W\\[ RXKST R\\DRE',
	    'Po\\GlG RRK[K R`NjN R`RjR R`VjV R]ZlZ RWEW[ R]K][ R`J`V ReCeJ R`JjJjV RWM[Q RWKRV R[DRE',
	    'Po_IlI RRK\\K R_NkN R_TkT R_ZlZ RYEY[ R_H_[ RfGfZ RXN\\R RXKST RaC\\M RgCeJ R\\DRE',
	    'PoSK[K R_MjM R]ZlZ RXEX[ R]E][ ReHeW R]ElEl[ RWM[Q RgQjU RbQ_U ReLhS RWKRV ReKaT RiG_I RZDRE RWHVO',
	    'Po]FjF R\\JkJ RRK[K RWEW[ RdCdJ RWM[Q RcO]U R`SdXk[ RfJfOlOlM RWKRV RaJ_N\\O R`SiSeWaY[[ R[DRE',
	    'Po]KcK RfKlK RRK[K R]RcR RfQlQ R_[b[ RXDX[ R]DcDcZ RWKRV R]D]S[Z RfDlDl[h[ RZDRE RWHVO R]O\\Y RfDfTeZ',
	    'Po^IjI RSK\\K R^NjN R\\RlR RXEX[ R^E^N RdEd[ R^EjEjN RWL[P ReSlY RdRfT RdR]Y RWKRV R[DSE',
	    'PoSEkE RZMdM RRUlU RZJZM R_C_F RZJdJdM R`UjZ R_USZ RgQWS RVHiHiOVOVH R`R`[',
	    'Po`IjI RRK\\K R`MjM RaWiW Rh[l[ RXEX[ RaQaX ReFeO RaQiQiW R^ElEl[ RWM[Q RWKRV R[DRE R^E^R\\Y',
	    'Po\\GkG RRK[K R[TlT RXEX[ RcCdG RcQc[ RWL[P R_KcO RgLlP RhIeP RWKRU R`I\\Q R[DRE',
	    'Po\\HlH RRK[K R]RcR RWDW[ R]K]R R]KcKcR RVMZQ RhCjF RfU`[ RjLeW RWKRU RdT[V RZDRE RWIUQ ReCeMgY RlWk[i[',
	    'Po_LlL RTP]P RTU]U RTITU RXJXU RRK]K]U RfEf[ RbS^V RVF[FYJ ReKjU RVCSJ ReKaT RjD_F R]XXYRY',
	    'Po]GlG RRK[K R^QkQ R`[d[ RXDX[ R^J^Q RdQd[ R^JkJkQ RWM[Q RiTkY RcCeG RWKSV R_T^X R[DSE RWHWO',
	    'PoRIlI RWWhW RWZhZ RWSW[ RUF\\E`E_N RWShSh[ RaObQ RgOiR RVPTR R[O\\R RaJlO R\\KRO RhD_E',
	    'Po\\JlJ RRK[K R]PjP R\\TkT R\\XlX RWDW[ R\\U\\X R]L]P RdPd[ R]FiFiJ R]LjLjP RXNZR RWKRU RbC`J RWIUQ RZDRE',
	    'PoSK[K RgOjO R[TlT RWEW[ R_C_P RdEkEkN RVMZQ RcSeWgYl[ RWKRU RdObO RbV_YZ[ RcQaX RbH[I RbLZN RWIUQ RgEeM RbEZERE',
	    'PoRK[K R\\KkK R]UlU Rj[l[ RXDX[ RbObZ RgOgZ R\\FkFkK R\\OlOl[ RcCdF RWKYQ RWKRV R\\F\\R[Z RZDRE',
	    'Po]IlI RSK[K R]MlM R^SkS R^VkV Rg[k[ RXEX[ R]D]M R^O^[ RdDdM R^OkOk[ R]DlDlM RWM[R RWKRV RZDRE',
	    'Po_GlG RSK[K R_MhM R_PjP RXEX[ R_I_P R_IjIjP RbP\\V RXMZR R_TbWj[ RWKRU R`C]I R`TjSfWbY[[ R[DRE',
	    'Po]DlD RRK\\K R_MjM R^UlU R^ZlZ RXEX[ R^P^[ R_H_M RePeZ R_HjHjM R^PlPl[ RWL[P RWKRU R[DRE',
	    'Po[ElE RRK[K RhPkP R[TlT RWDW[ R]I]Q RaIaP RfIfP R]IkIkP RiXl[ RVMZQ ReWiX RWKRV RbV`Y[[ RcEaK RcQbX RZDRE RWHVP',
	    'Po^GcG RRK[K ReNlN R^VjV R^ZjZ RXEX[ R^C^M R^Q^[ RfCfN R^QjQj[ RlKlN RWM[Q RbNbQ RWKRV RkFeH RcL[M RZDRE RWHVO',
	    'Po\\HlH RRK\\K R^OkO R^SkS R^WlW R[[m[ RYEY[ R^L^S R^LkLkS RXN\\R RXKSU R[DRF RkD^F ReEe[',
	    'PoRK[K R^LjL RfPlP RXEX[ R^D^L RfLfP RjDjL RWL[P RbP\\W RgDaJ R`TdXk[ R`FgH RWKRU RaL`P\\Q R`SiSeXaY[[ R[DRE',
	    'PodHjH RRK[K RbMlM R^SjS R^VjV Rf[j[ RXEX[ R^O^[ ReDeL R^OjOj[ RWM[Q R\\EkEkG RWKRV R_G\\N RWIVP R[DRE',
	    'PoRK[K R^QjQ R[UlU Rg[j[ RWEW[ R^M^[ RdKdU R^MjMj[ RWM[Q RcFdJ RiFkJ RWKRV R_E\\J R[DRE ReD^E RhD`E',
	    'PoRK[K RaTlT RaYlY RXEX[ R]N][ RaNa[ RaNlNl[ RWM[Q R]H_L RcGdK RkGhM RWKRV R[DRF RjD\\F',
	    'PoRK[K R^MjM R]RjR R`[h[ RXEX[ R`U`Z RhXh[ R^IjIjQ RcTfV R`D\\I RXMZR RjUkY RWKRV R\\U[Y R_FgEeI R[DSE RWIVP',
	    'Po[IlI RSK[K R[PlP R\\SjS RXEX[ RaEa[ RfEf[ R\\LjLjS RWMZQ ReSlZ RaSZZ RWKRV RcD\\F RjDdF RWIUP RZDRE',
	    'Po[FlF RSK[K RXEX[ RaCaI RgCgI R[P_S RWM[Q RdOhT RhKeP RjOfX RWKRV R_K]P RaO]Y RZDRE RWHVO RaVbZ[Z RjVkZdZ',
	    'Po[HlH RRK[K R\\SaS RXEX[ R\\L\\S R\\LaLaS RWMZP RiDjF RiJeO RWKRV RkNfScT RkSfXaZ RaV[W RZDSE RlWkZiZ RfDfMhX',
	    'Po^HjH RRK[K R^LjL RfPlP RWEW[ R^D^K RdDeLfP R^DjDkK RWM[Q RbP\\V R_TcXj[ RWKRV RaL_O\\Q R_TiSeWaY[[ R[DRE',
	    'Po[FlF RRK[K R[OkO R]VjV R]ZjZ RXEX[ R]R][ RdRdZ R]RjRj[ RWM[Q RgLlP RdK`O RcE_J RhHcL R]IbK RWKRV R[DRE',
	    'Po_HkH RRK[K R`NjN R`QjQ R`TjT R]WlW RWEW[ R]L]W R_D_H R`K`T R`KjKjT RWM[Q RgXl[ RWKRV RaX[[ RWIVP RZDRE RiD^F',
	    'Po]IkI RSK[K R_LkL R^RlR R_VkV RXEX[ ReJeM ReRe[ R]DkDkI RWM[Q RaNaQ RiNhQ RWKRV RZDRE RWHVO R]D]Q[Z',
	    'PoRK\\K R^TcT RfTkT R^YkY RYEY[ R^N^[ RdGdJ RfNkNk[ RXN\\R RiEkK RXKST R_E]K RdM^N R[DSF RiD_E',
	    'Po]KkK RRK[K RXEX[ R\\F\\J RdCdF R\\FlFlJ RWM[Q RjNgQ RWKRV RbL\\O RbQ[T RdSaVZY R[DRE RaMdQe[a[ ReKgQjW',
	    'Po\\GlG RdKlK RRK[K R_QlQ R^WkW R^ZkZ RXEX[ R^S^[ R_K_Q RdGdK R^SkSk[ RWM[Q RfDjE RWKRV R[DRE RiM^O RcDaI`K]L',
	    'PoSEkE RXJfJ RYPeP RhQjQ RRUlU RTLTQ R_C_F RUS`S_[ RYNYOeNdP RXGXHfGfJ RTLjLjQ R_UfYlZ R_UXYRZ RfR`S',
	    'Po]FlF RRK\\K R_NjN RaXhX Rh[l[ RXEX[ R]P][ R_I_M RaSaY RdCeF RaShShX R_IjIjM R]PlPl[ RWL[P RWKRU R[DRE',
	    'PoRF_F RSI^I RiMlM RRS^S RXCXI RYOY[ RSNSL^L^O RcEiEiL RlJlL RYSRY RbQdUhYl[ R`PjPhTdX^[ R\\OTP RcEaL RYT^X',
	    'Po\\FlF R\\KlK RRK[K R`OiO R`SiS RXEX[ R`K`X RaCaI RgCgI RiKi[ RWM[R RWKRV R[DRE RcW\\X RiWaW RkVeW',
	    'Po\\FkF RRK[K R]MjM R]RjR Ra[h[ RXEX[ R]J]Q RaUa[ RdCdQ RhXh[ R]JjJkQ RWL[P RiUkZ RaSeU RWKRU R]V[Z R[DRE',
	    'PoRK[K R_ZiZ RWEW[ R]F]J R_S_[ RdCdG R_SiSi[ R]FkFkJ RWM[Q RdMkS RgJkM RdM\\T RaJ]M RWKRV R[DRE',
	    'PoSK[K R_OhO R\\SkS R`[d[ RWEW[ RdSd[ RWMZQ RhUkX R[I_K R]GaI RgKlP R^U\\Y RaJ\\P RdEhM RfFkFiK RWKRV R_C\\I R^FcF_M RWIUP RZDRE',
	    'Po\\FlF RSK[K R]MlM R_QjQ RXEX[ RdCdF R_JjJjQ RfUjY R_SaU RWM[Q RdQgW RjTfU RWKRV RbW]Y RdGd[a[ RWIVP RZDRE R\\F\\R[Y',
	    'Po^IkI RRK[K R^MkM RXEX[ R^E^L R^EkEkL RWKYQ RiNlR R`O]R RWKRV RdMeR^T RhU^X R[DSE RjXcZ\\[ RWHVP',
	    'Po\\HjH RRKZK ReNkN RZNaN RWEW[ RWMZP RjKlM RgEjH RcK\\R RcJiPlR RgIfN RWKRV R]I[M RaC_H RcQ]S RgS^W RiVdY\\[ R[DRE',
	    'PoTFlF RWI`I RbIkI RiPlP RVUlU R[G[P R^K^O R`C`F RYRaRa[ RfGfP RjKjP RcWk[ RbKbP RaT^WV[ RXLWP RTFTSS[ RgQ`R',
	    'Po]IkI RSK[K R_LcL RhLlL R_OcO RhOlO R`TkT R^WlW RWEW[ ReJeO ReQe[ R]EkEkI RWM[Q RWKRV RaQ_U RZDRE R]E]R[Z',
	    'PocElE R\\JbJ RRKZK RcOlO R\\ObO Rh[l[ RWEW[ R\\E\\X R\\EbEbO RiQi[ RlWl[ RWOYR ReV`[ RhOdX R`RaV[W RVJST RdIcO RYDRE RiKgRhT RiGgR',
	    'PoSKZK RXEX[ ReDjDjM RWMZQ R^G_H ReGgH RdMfPlR RWKRV RbI]K RjIdK R]DcDcLaP\\R RdR_T RfT_X RhWdY][ RZDSE',
	    'Po_LkL RTO]O RTT]T RTITS RWVWZ RYJYS RSK]J]S RfEf[ RiSlV RaS^V RZVZZ R\\V^Y ReKiT RVCSL RVF[FXL RTVS[ ReKaT RjD_F',
	    'Po]EkE R^HjH RSKlK R^PjP R^TjT R^WjW RXEX[ R^M^W RdCdJ R^MjMjW RXM\\R RgYl[ RXKSU RaX\\[ R[DSE',
	    'Po_EkE R`MkM R`RkR RRR^R R`VkV RYNY[ R`I`V ReEdI R`IkIkV RYRRY RgYlZ RTCTK]K]I RbX][ R\\ETG R\\NSO RYS\\W',
	    'PoRK[K R^OjO R^SjS RWEW[ RaVa[h[hY R^KjKjS RcTeV RWM[Q RjVkZ R^G_I RcFcI RiFhJ RWKRV R]V\\Z R[DRE RjC^E',
	    'Po]KjK RRK[K R]NjN R\\TkT R\\WkW RXEX[ R\\Q\\Y R^G^N R\\IjHjN R\\QkQkW RWM[Q RaD[I RWKRV R[DRE R_EgEdHd[lZl[',
	    'Po]FkF RSK\\LlL RaWgW R^ZkZ RXEX[ R^P^[ RaSaV RdCdL RaSgShV R^PkPk[ RWL[Q R_HbJ RiHgJ RWKRV R[DRE RWHVP',
	    'PoSHlH RVPgP RVRgR RVTkT Rf[j[ RZXZ[ RUFZF_E_L RVNgNgR RcXeZ RUXS[ R_X_[ RVNVWjWi[ RZKRM R_HTM R_HgLlM RgD]E',
	    'Po]HjH RRK[K R]LjL R\\QkQ R[UlU RXEX[ R]D]L RaNaU RdDdL RgNgU R]DjDkL RhXk[ RWM[Q RWKRU R`Y[[ R[DRE RWGVO',
	    'Po]FlF R]JlJ RRK[K R^RjR R^VjV RXDX[ R^N^V RaCaJ RdJdV RgCgJ R^NjNjV RgXjZ RWN[R RWKRV RbX\\[ RZDRE',
	    'Po^GkG R^JjJ RSK[K R^MjM R^PkP R\\TlT RXEX[ R_F_P ReEe[ RdSkZ RWMZQ RdS]Y RWKRV R_C]J RWIUP RZDRE',
	    'PoRK[K ReNlN R[NbN R[QlQ RXEX[ RfXi[ RVKZP ReGhJfM RlXk[ R[G^J\\M RaKaN RkKlN RgOjP R]TbV RjFgL RWKRV R^D\\H R`F^K RhDfH RiTeY`[ RdPfY R^Q[Z RZDRE RdCcLeX RWHVO',
	    'Po\\FkF RSK[K R]LjL R]OjO R\\SjS RXEX[ R]I]O RdCdR R`U`[h[hY R]IkIkO RcTeW RXMYQ RhQkT RjVkZ RWKRV R\\V[Z RZDSE',
	    'Po^FjF R\\IlI RRKZK R\\MbM ReMkM RdQlQ R\\SlS Rf[l[ RWDW[ R_K_Q RdCdI RfSf[ RhKhQ RlXl[ RVMZQ RWKRU RaT_X]ZZ[ RZDRE RWIUQ RcP\\Q',
	    'Po\\IlI RRK[K R\\NkN R]QjQ R[UlU RWEW[ RaCaI RfCfI RWM[Q RjDiG R_J`M R]D^G RdNcUhYl[ RhJgM RWKRV RcV`Y[[ RWIVP R[DRE',
	    'Po\\HlH RRK[K R`OhO R`XgX Rg[k[ RXDX[ R\\Q\\[ R`T`Z R`TgTgX R_LhKhO R\\QkQk[ RVMZQ RfHkM RbH\\N RWKRV R\\EdE`K_O RZDRE RWHVP RiDaE',
	    'Po]IkI RRKZK R]LkL R]PkP R]TlT RWDW[ R^G^U RaWa[ RdHdT RVMYQ ReWf[ RjVk[ RdDdF RWKRU R_C\\J R]W[[ RZDRE RVHUO',
	    'Po]FkF RRKlK R^PkP R^TkT R]WkW R[ZlZ RWEW[ R^M^T R`GaK RdCdF RdMdZ R^MkMkT RWM[Q RWKRV RhFhJ R[DRE',
	    'Po^GjG R^KjK RSK[K R[MlM R]QbQ R]TbT RXEX[ R]M]X R^D^K RbMb[ R^DjDjK RgVl[ RWM[Q RjUd[ ReRhY RdQkQhX RWKRV RbX\\X RZDRE RWHVO',
	    'PoRK[K R]KkK R]OkO R\\QlQ R^WjW R^ZjZ RXEX[ R^G^N R^S^[ ReHeN R^SjSj[ R\\IkHkN RWN[R RaC\\I R`EgEdH RWKRV RZDRE',
	    'PoaIiI Rb[l[ RWEW[ RgOgW RRK[J^L ReNkT R\\D^F RdEhDjI ReIaM RVMZQ^P^WZZ RjLgPbU RWKRU RaE_I ReNbP_Q RfXbX RZDRE RWHVP',
	    'Po]IjI RRK[K R]LjL R\\NlN R^QjQ R\\TkT RWEW[ R]E]L R`T`Z RaCaL RfCfL R]EjEkL RWM[Q RdUhYl[ RWKRV RjVfW RWIVP ReY]Z RZDRE R\\N\\T[Z',
	    'Po\\FlF RRK[K R[NlN RaWgW R]ZkZ RWEW[ R]Q][ RaTaW RdCdN RaTgTgW R]QkQk[ RWM[Q R_IaL RhIkL RWKRV R_G]L RhGfL RZDRE',
	    'PodEjE R[IkI RSK[K R\\MkM R^PdP RWEW[ R_E_I RdCdI RVMZQ RaPbW^Z RhJkK RdRbX RWKRU R_S^V RZDRE R\\M\\SZY RgIgOh[k[lY RkPhWd[',
	    'Po\\FkF RRK[K R\\LkL R]OkO R\\RlR RWEW[ R`U`Z RaJaR RdCdF RgJgR RWM[Q RgHkI RWKRV R`H\\J RcT[W RkUfW RfY][ RZDRE RdSfWk[',
	    'Po\\DkD R^JaJ RfJiJ RRKZK R]MaM RfMjM R[PlP RWDW[ R\\G\\J R\\T\\[ RaTa[ RdDdN RfTf[ R\\GlGlJ RVMZQ RWKRV RcPaU R\\TkTk[h[ RZDRE',
	    'PobGfG RgJjJ RSK[K RgPjP R[UlU RXEX[ R]E]U RbObU RbCbKeKeU RfEjEjU RgWk[ RWM[Q R_YZ[ R`W\\Z R_D\\F RWKRV R]J`J\\J R]O`P\\P RZDRE',
	    'Po_HiH RSK[K R^RjR R^TjT R^WjW RXEX[ R_N_W RdHdM R\\OjOjW R]F]GlFkH RWM[Q RdCdF RgXl[ RWKRV RaK\\L RbX\\[ RhK\\O RZDRE',
	    'Po[FlF RSK[K R^O`O R]WiW RWEW[ R[J`J`O RcCcF RcJcO RfJfN R^RiQi[ RVMZQ RaHbI RfHdI RiIlO RWKRU R]J[N RkIfI RiNeN RWIUQ RZDRE R^R]Y',
	    'Po_IjI RSI]I R^KlK R`QkQ R`TkT R`WkW RYEYO R_E_I R`N`W ReCeK R_EjEkI R`NkNkW RZK]N RXI[L RYIRO RgXl[ RVQ[Q\\XZX RbX][ RVQUY R\\DSE',
	    'Po^EeE R_IiI RSK[K R]LkL R\\QlQ R^TiT R`[h[ RXEX[ RaWa[ RcFcH RcIdL R^NiNiT RbVeW RWM[Q RhWlZ RhEkH RWKRV R]E\\I R]W[[ RWIUP RZDRE RiCaD',
	    'Po\\DlD RSK[K Rh[k[ RWEW[ R\\J\\[ RdDd[ R\\JkJk[ R_TbW RVMZQ RhFlI R_MaO^Q RbS`X RiLgP RfMiPeQ RiSgX RfThWeY R_F\\I RWKRV RZDRE RWHVP',
	    'Po[FlF R]KkK RRK[K R^MjM R^OjO R^RlR R\\UjU RWEW[ R^J^R R`C`H ReIeR RgCgH RWM[Q R^I[M RWKRV R`WeYl[ RiVcY[[ R[DRE',
	    'Po]FlF R_JkJ RRK[K R]MlM R`SjS RXEX[ RbGbM ReMeW RhGhM R`PjPjW RWN[R RdCeF R`P`WiXl[ RWKRV RbX][ RZDRE R]F]R[[',
	    'PoRK[K R^KkK R^VjV R^ZjZ RXEX[ R^S^[ ReHeN R^SjSj[ R]IkHkN RWM[Q RePgR R^G^OjOlS R^P\\S RaD\\I R`FfFdH RWKRV R[DRE RbPaR',
	    'Po[FlF RRKYK R[MkM R^PcP ReTlT RWDW[ R^N^U RaFaM RaWb[ RfNfT RfFfM R[IkIkM RVOYR RjWk[ RcCdF ReWf[ RWKRU R]W[[ RkPeQ RcT]T RYDRE RVHUP R[F[RZZ',
	    'Po\\ElE RSK[K R\\LaL RfLkL R[NlN R`XgX R]ZjZ RXEX[ R\\H\\L R]Q][ R\\HaHaL RcCcN RfHfL R`TgTfX R]QjQj[ RfHkHkL RWM[Q RWKRV R`TaX RZDRE',
	    'Po\\FlF RRK[K ReKkK R\\NkN R]QjQ R[TlT RWEW[ R]KaK`T R]HbHbK RdCdF RgKgT ReHkHkL RWMZQ RdUhYl[ RWKRV RdT\\X RkVfW R`V_[ R]H]L ReHeL ReY]Z RZDRE',
	    'PoSKZK R]MkM R\\RcR ReRlR R\\TcT RXEX[ R\\O\\[ R]I]M RaIaM R\\OcOcT RfIfM R]IkIkM RWNZQ RfTgW RjFhI R^F_H R\\EeEdH RWKRV RbWcZ\\Z RjOj[g[ RZDSE RkCdD',
	    'Po[DbD RdDlD RRKZK R[MlM R[TkT R^VcV RWEW[ R\\G\\K R^S^[ R`M`S ReGeK ReGkGkJ R[PkPkT RfMf[lZl[ RVMYQ R\\GaGbJdM RhHhJ RWKRT R_H_J RkVeW RcZ]Z RYDRE R[M[SZZ',
	    'Po\\FbF ReFkF RdIlI RSK[K RiMlM R^QiQ R^TiT R^WiW RXEX[ R^O^W R^OiOiW RWN[R RfXk[ RWKRV R\\D[G R_C^I\\M ReDeG RfIeM R[IaIaM_M RaX[[ RhChHiJiM RZDRE',
	    'PoSHSL R_C_G RSHjHjL R[OWWR[ RdNhWl[',
	    'PobMkM RU[k[ RRFRI R_C_F RbFbL RRFlFlI RlWk[ RUWUZ R[FZJXLSN RUPePUW',
	    'Poe[l[ RSFSJ R_C_F RTRfRf[ RcFcMjNjL RSFkFkI RlWl[ R\\FZKXMSO R^O]UZXS[',
	    'Poa[g[ RRFlFlJ R^DaF RfKiL RcIhK RZIXKSM R\\TXYS[ RRFRJ R^L[V RSQiQg[',
	    'PobMkM RRFRJ R_C_F RcFcM RRFlFlI R[FZJXLSN R^M[QST RZQhQcUZZ RXRaWT[',
	    'PobLjL RVSiS Ra[h[ RRFRI R_C_F RcFcK RTNiNiR RRFlFlI RVRTVkVh[ R[HXKRL',
	    'PoUSiS RR[l[ R_C_G R_S_[ RSGkGkJ R[HZMXNSP RcGcOjOjM RSGSK',
	    'PoUOjO RRSlS RZ[_[ RSGSJ R_O_[ RSGkGkJ R^C`E RbIjL R\\IUL',
	    'PoRQlQ RSGSJ R_M_[ RSGkGkJ R^C_E RcIjL R_PeVlY RZJTM R_PXWRY',
	    'PobKjK RSOkO RRTlT RRFRI R_C_F RaFbK ReMe[ RRFlFlI RZMYVWYR[ R[GXKRL',
	    'PoRSlS RRGRJ R_C_G RRFkFlI RbFaKiKhK R[HYKTM R`TdYl[ RUPZP_P]VZXWZR[ RhN^O',
	    'PoTNjN RUTlT R_[d[ RSFSI RVPUU R_C_F RdNd[ RSFkFkI R\\HYKSL RdS\\WRZ RbFbKjLjK',
	    'PobKiK RZOaO RUO_O RUViV R_[l[ RRFRI RUQUX R_K_[ R_C_F RbFbJ RiQiW RRFlFlI RkXl[ R[GXJRL RiM`N',
	    'PobNkN RRRlR RSFSJ R_C_F RcFcM RSFkFkJ R\\FZKXMSO R`RbVdYl[ R_N]VZYR[',
	    'PobLjL RWPjP RRUlU RSFSI R_C_F R_L_[ RbFbL RSFlFlI RYMTR R[FZIXKRM',
	    'PocLkL RVX\\X Re[j[ RSFSI RWNWX R\\U\\X R_C_F RcFcK RSFlFlH R\\HYKSL R^OkOkUj[ R[QRR RdObV^[',
	    'PoXPlP R^SjS R^WkW RSFSI R^O^[ R_C_F RSFlFlI RZNRU R[HYKTL RcFcKjKjJ',
	    'PobKiK RURiR RUViV RUZiZ RRFRI RUNU[ R_C_F RaFbK RUNiNi[ RRFlFlI R[GXJRL',
	    'Po_MeM R\\[l[ RSFSI R_C_F RSFkFkI RVWR[ RUN_N_Q R\\GYJSK RTRiR`VXX RYZa[ RaFaJiIiJ RhLaM',
	    'PoWVbV RW[k[ RSFSI RWRW[ RWRbRbV RSFkFkI R]C`F RkXk[ RYLRS RbHjL RhWeW R[HTL RWOiOhW',
	    'Po_PkP RUY[Y RRYXY Re[j[ RSFSI R_C_F RSFlFlI R[U^X RTQXT RbV^[ RXNTQ R\\PYSTY RbFbKjJiK R\\GYKSL RdMbW RkRj[ R]XWX',
	    'PoRMlM Rd[i[ RSFSI RTQT[ RTQjQj[ RSFkFkI R^C`F R_SeV RbHjK R_S\\VWX R\\HTK R_N^U',
	    'PobKiK R]YfY RXZaZ RSFSI RWN`N_Y R_C_F RbFbK RSFkFkI RdUf[ RgSkZ RVSS[ R\\HYKUL RgOiY RVNUX RdM^N RhM`N',
	    'PoaKjK RVRiR RTZjZ RRFRI RTTT[ RVNVR R_C_F R_L_Y RaFbK RiNiR RjTj[ RRFlFlI R[GXJRL',
	    'PofWjW RRFRI RTNTV RaMa[ RaMkMkW RRFlFlI R^C`F RcHjK R[XT[ R[HTK R\\O\\URU R]LTM',
	    'Po]NlN RRN[N Rd[i[ RSFSI RWNWX RSFlFkI R_DaF RcIjL R[ITL RbN`UjUj[ R\\WRY',
	    'PocRgR RRWfW Re[k[ RRERH RWLhLgR RRElElH R\\OaP RcHkK R[HSK RWLWTlTk[',
	    'Po\\NkN RZSlS R_[c[ RSFSI RcNc[ RSFkFkI R^C`F RXVS[ RRQWR RbHiK R[HTLXN',
	    'PobKiK RUNiN RSRkR RTWjW RR[l[ RSFSI R_C_F R_L_R R_T_[ RbFbK RSFlFlI R[GYKSL',
	    'PoaKjK RWNiN RRSlS RUZiZ RRFRI RUUU[ R_N_Z R_C_F RaFaK RiUi[ RRFlFlI RXMTQ R\\FYJTK',
	    'PobJiJ RTMkM RTSiS RUViV RR[l[ RRFRI R_C_F R_S_[ RbFbJ RRFlFlI R\\NWR RcOjR RZISK',
	    'PoZ[e[ RSFSI R[U[[ R_C_F RcFbKiKiJ RSFkFkI RgUl[ RVWR[ ReXd[ R]MYQ R\\SbV R\\HYJTL RURgP ReNiR',
	    'PobJiJ RRNkN RSFSH R_C_F R_K_N RbFbJ RSFkFkH RdPkS RZPSS RZS]VbYj[ R[FYJSK RdRaV\\YS[',
	    'Pob[l[ RSFRI R_C_F RbMbZ RbFbKjJiK RSFlFlI RlXk[ RTOYQ RiOdQ R\\M[UXYR[ R[HYJTL RcTjV RZTRV',
	    'PobKjK RVNiN RRRlR RVZiZ RRFRH RVUV[ R_C_F R_K_T RbFbJ RVUiUi[ RRFlFlH RXLTP RYIRK',
	    'PoTZjZ RRERI RTMT[ R_C_E RTMjMj[ RbFbKjIiK RRElElH R^K[M RYQfW R^O\\RWT R]QeQaUZX R[GXJRL',
	    'PoROlO RVShS RXZiZ RYUY[ R_C_F RVLhLhS RWViVi[ RSFSGlFkH R^L\\RXURW RZISK RbFbJiIiJ',
	    'PoROlO RTVkV R[[_[ RSFSI R^I^O R_U_[ RcGjK R[GTJ RVKfL\\RRU RXRgR_T RSFkFlI',
	    'PoUNlN RXY\\Y RSFSI RSFkFkI R^C`F RlXk[ RdLhM RaPdWfZj[ RcHiK RiRcX][ R[HTK R`KbU RUNUTTZ RUR]R\\Y',
	    'PobJiJ RTL]L R_NlN RRP^P R[[k[ R[P[Z R_C_F RaFbJ RSFSGkFkH RlXk[ RaSjW ReLaT RiNgTeV_X R\\FYITJ RVQUY',
	    'PoUQjQ RSXkX RRFRI RULUQ R_J_[ RRFlFlI R^C`F RULjLjQ R[HTK RcHkJ RSTkTkX RSTSX',
	    'PoWPgP Rf[k[ RSSS[ RSSkSk[ RRFlFlI R^CaF R^TgX R_UWX RWLgLgP RcHlJ RWLWP RZHRK RRFRI R_P^U',
	    'PobJiJ RaPfP RUV]V RaVjV RUZjZ RUSU[ R_C_F R`RjRj[ RRFRGlFlH RYMgMfP R[KWORP RaFbJ R\\FYJSK R]QTR',
	    'PoUPjP RYUiU RXZjZ RSFSI RXWX[ RYRYU R_C_F RaPaZ RiRiU RjWj[ RSFlFlI RULjLjQ RULUSTY RbFbJjJjI R\\GXJUK',
	    'PobJiJ RVOhO RVShS RSUkU RSFSH RVLVS R_L_[ R_C_F RbEbJ RVLhLhS RSEkEkH R_UfYlZ RYISK R_UWYRZ',
	    'PoSFSGkFkH R^C`F RbMkR RUM^R RaTeYk[ RUV^Z RbHjJ RTL]LZOSR RaLjLgO`R RTT]TYXR[ RaTjTgX`[ R[GTJ',
	    'PoRMlM RUU]U RUPUU RUP]P]U RSFSGkFkH RdVh[ R^C`F RbPeY RdJhL RhRbY][ R[HTK RaLdW RbHkJ RlXk[i[ RaJcU R_XRY',
	    'PobJiJ RUMjM RRUlU R_T_[ R_K_M R_C_F RaFbJ RSFSGlFkH ReOaS RZOUT RdQjT RYQ]S R[GYJSK',
	    'PoTOhO RTRhR RUV^V RT[\\[ Ra[l[ RTKTR RUSU[ RaSa[ RTKhKhR RRFRGkFkH RkXl[ R^C`F RbHjJ R[HRJ RjU`W',
	    'PoRO\\O RRS]S Re[l[ RSFSI R_L_U RfTf[ R_LiLiU RSFkFkI RXLXT\\X R_C`F RWVRZ RcVaY\\[ RbHjJ R\\HTJ RdPcX RXOWW',
	    'PoUKiK RRUlU R_I_N RRERGlElH RSNkNiQ RXP]R RTRYS RbGlI RbXl[ R\\GSI RaQ`V\\YR[',
	    'Po_KjK RUQiQ RUUiU RRXlX RUNUT R_I_N R_U_[ RUNiNiT RSFSGkFkH R^C`F R[HSJ RbHjJ',
	    'PoTLjL RRQlQ RVWhW RVZhZ RVTV[ RVThTh[ RSFSGkFkH RYMZP R^JaL R_C_F ReMcQ RcHkJ R[HSJ',
	    'PoaJiJ R^OhO RYYfY Rf[k[ RSSS[ RWLWR RYUYZ R_C_F RYUfUfY RWLhLhR RSSkSk[ RRIRFlFlI RaFaJ R^O^S R[GXJRK',
	    'PobJiJ R[NjN RZRkR R[VkV RY[l[ RRERI R_C_E RaEbJ RcLcZ RSElElI RXVS[ RRQWR RTMXN R[FZITK',
	    'PoSL^L RTO]O RRR^R ReSiS RRUlU RYJYS RSFRGlFlH RcN`R RdKbQ RcGjI R\\GTI R`UcYl[ R^U[YR[ R_KjKiR',
	    'PobJiJ RZ[][ RRFRH RURU[ RUR]R][ R_C_F RaFbJ RRFkFkH ReVj[ R_Vd[ RbS_V RiReV RYL_LhP RWT[U RWW[X RZPcOlP R\\FYITJ R_KXORP',
	    'PoSMlM RRUlU RR[Y[ R_I_R RSESFkElG RXJ[M RfJcM R_MePkR R\\GTH R_MXQSR RbGkH R[Yj[ R\\SWX_Y RaYT[',
	    'PobJjJ RUP^P RbQkQ RSV\\V RY[^[ Re[j[ RSESH RUMUU RUM^M^[ R_C_F RcEcJ RaLkLkP RSElElH RUR^S[WSZ RaQ`UkUj[ R\\FZJSK RYKYM',
	    'PoaJiJ RSNkN RURiR RRUlU RRFRH R\\X\\[ R_C_F R_N_U RaFaJ RRFkFkH RhWl[ RYK[N RaWc[ RVXR[ ReKbN R\\FXISJ',
	    'PoaIiI RYLgL RXSjS RRVlV RUZjZ RUWU[ R_C_F R_R_Z RaFaI RjWj[ RRFRGlFlH RZPVT RZL`O RUM\\P R[FYISJ R^JSM RfM_ORQ',
	    'Po\\TkT R`[d[ RVMV[ RZMZX RdRd[ RSFSGkFkH RgVkZ R^C`E R`V\\Z RVNRR RaJ\\N RWJTP RbGjI R`NePkR R[HTJ R`LiLfNbQ\\R',
	    'PoRQ^Q RXKX[ RhKh[ RSFSGkFkH RXR[U R^CaF RVTRX RUMUP RaMeN R`QdS RWRUW RXPVU R\\M\\O R[GTJ RcHkJ RlU_W',
	    'PoaJiJ RSESH RXNXY R_C_F RdNdY RSEkEkH RaEaJ RYISK R\\L^X RiMkZ RUMUSSZ R`M`T_Z R^LTM RjL`M R[V\\YTZ RgVhZaZ',
	    'PoVJhJ RWPgP RWRgR RWUgU RRXlX RWMWX RWMgMgX RSFkFjH R^C`E RbGjI RcYk[ R^H^M R[GSI R[YS[',
	    'PoaIiI RVNhN RVPhP RVSlS Re[i[ RZXZ[ RVKhKhP RRERFkElG RdWfY RUWS[ R^I\\K R_W_Z RaEaI RVKVUkUi[ R\\EYHSJ',
	    'PoaHiH RRMlM RVOhO RUTiT RRVlV RS[Z[ RUQUT RVKVO R_I_T RaEaH RVKhKhO RUQiQiT RSESFkEkH RYHSJ R[Yi[ R[TWX`Y RbYW[',
	    'PoTPZP RTSZS RiWlW RTLTX RRL[LZ[ R^M^V RfMf[ RfMlMlW RSFSGlFlH R_DaF RcNcW_[ RcHkJ R[HTJ RcL^M RcU]U R[VRX',
	    'PoROlO RY[f[ RZVZ[ RVL_K_T RSFSGkFkH RXM[O R^UaY RhWlZ R^C`F RfMcO RgYf[ RUWSZ R`PjU R_OSU RcHkJ R[HSJ ReJ^K',
	    'PobIiI R]LjL RTPZP R\\SlS RRTZT R^ZjZ RTLTQ RZKZ[ R^U^[ R_C_F R^UjUj[ RSFSGkFkH RbFbI R^OhOiS RUTSZ RbLaS R\\FYISJ',
	    'PobJiJ RRO\\O R_OjO R_RjR RRS]S R_UjU RSERH R_L_U R_C_F RbEbJ R_LjLjU RRFlFlH RaU`Y[[ RgUg[l[mY R[FYJSK RXMXTUXS[ RXU[Y',
	    'PobIiI R]NdN RUTiT RUKUT R_C_F R[V[[eZd[ RUKiKiT RSESGkEkH RgWl[ R^UaX RVXS[ R_I\\K RaFbI R^NXOdR R\\EYISJ RcO^RYS',
	    'PobIiI RUPiP RUTiT RRVlV RUMUS R_M_[ R_C_F RUMiMiS RSERGkElH RWKYL RYJWK RfJcKfL R_J]K`L RaFaH R_VdYl[ RZHSJ R_VZYR[',
	    'PoaIjI RTS^S RRV^V R_C_E RaEaI RfVk[ RdV`[ RUQST ReMeThY RXW]Y RbK`Q RXWVZR[ RSERFkEkG RaNkNjQ RYRXX RePdW RZHSI R]KTLTO]O]P',
	    'PoaIiI RRL_L RSO_O RTR^R RRU_U RV[Y[ RYJYO RYUY[ R_C_F RaEaI RRERGkEkH RfVk[ R\\W^Y RdV`[ RUWRZ ReMeShY ReScY RaNkNjQ RbJ`Q RZHSJ ReOdV',
	    'PoaIhI R_RgR R\\VjV R]XiX RZ[l[ RRFRH RSLS[ R^C_F RcRc[ RRFkFkH RVRYU R^T[W RSLYLVQ R`LZN R^McOkR R\\FYITJ RaFaJ R^MgMbPZR RYXUX',
	    'PoSL]L R_OjO RXR\\R R_RjR R_UjU Rf[l[ RTQTY RXLXX R_L_U RgUg[ R_LjLjU RSFSGlFlH RlXl[ R_C_F RbW`Y\\[ R[GTJ RbHjJ R]WRY',
	    'PoZJaJ RVK^LgM RXMaM RUM\\M`NhO R[OeO RUO[O`PjQ RVU]U RSU[U RaUhU RbXjX RTX[X R`XgX RRXYX Rd[l[ RXSX[ R^J^P RdSd[ RSERFkEkH R^C`E RbGjI R[GTI R]ZVZ RdI\\J R[RTS RhR`S',
	    'PoSKkK RXSfS RXVfV R[[_[ RSFSH RXOXV R_V_[ RWPgPgV RSFkFkH RXWU[ RiMfP R]KWP R_C`F RaKePlR RdXiZ RUMYPRR RcGkI R\\GSJ',
	    'PoaHiH RVJhJ RRLlL RUSiS RTWjW RTYjY RTUTZ R_C_F R_H_L RaEaH RUNiNiR RTUjUjY RSERGkEkH R_N_[lZk[ RdPiQ RZHSI RYPUOUQYP R[P_PcP_P[P',
	    'PoTN]N RaNjN RTQjQ RUT\\T R_TeT RUW\\W R_WeW RSESH RTLTQ RUSUZ R_S_Z RaLjLjQ RSEkEkH RiZk[ R\\EYISJ RhSiZ R\\YRZ RgY]Z RaEaIiHiI R\\KTL',
	    'PoaJiJ RTO^O RTR^R RRT_T RX[\\[ RRERH RTLTQ RTL^L^Q R_C_E RaEaJ RREkEkH RVWR[ RbOdUhYl[ RXRXTUZ RcK_R RbOjOfVbY_[ RWW]V\\[ R\\EYITJ',
	    'PobJjJ RUP^P RaQkQ RSV\\V RaZlZ RY[^[ RSESH RUMUU RUM^M^[ R_C_F RaUa[ RaLaQ RcEcJ RfQdU RaLkLkQ RaUlUl[ RSElElH R]EYJYM RUR^S[WSZ RZITK',
	    'PoaHiH RRJlJ RbOjO RTQjQ RVWiW RTNTQ RaEaH RVSiSiY RTNjNjQ RSERFlElH RVSVYYY RYUUU RgLjL R_H_LcL RYL\\L R`Yl[ R[GTH RWLTL R_K[L ReLbL R^N[OVP RfUiUeU R\\YXZR[ RhLeLhL RVLYLVL RZU]U[U RaU^UaUdUaU R]Y_Y]Y',
	    'PobHiH RVJhJ RRMlM RUR\\R RaRiR RTT\\T RTV\\V RaVjV RTY\\Y RaYjY RTTTY RUOUR R]O][ R_C_F R_H_M RaOa[ RaFaH RUOiOiR RSGSFkFkH RZHSI RaTjTjY R^[k[nY',
	    'PoSEkE RbHiH RaKlK RSK^K RRO_O RaPkP RaSjS RTU]U RaUjU RTW]W RaXjX Ra[l[ RSESH RTRT[ RULWO RaPaZ RbEbH RaJaMkMkP R[LZO R[HTI RTR]R][Z[',
	    'PobIjI RdLkL RRR]R RaYiY Rh[l[ RXLX[ R^T^[ R_C_F RbEbI RdKdN R`NjNjT R`N_TlTl[ RSERGlElH RgViY RWRZU ReQhR RePcR RWTRX RTNUP RdTbX RXPUV R\\M[P R\\EYHSJ R\\KSL',
	    'PoSJkJ RR[l[ R_C_J RfLb[ RXMZX',
	    'PoRI]I Rh[l[ RXDXI R`DhDiZ RlVl[ R]URW R[KYV RTKUU R`D`Q_X',
	    'PoRI]I R^MlM RXDXI ReCe[ R[KYW R^VRX RTKUU',
	    'PoTFjF RRMlM RRSlS R_C_G ReOe[ RYHZL ReHdL RYVVYR[ RYPYW',
	    'PoRI]I R]NlN RXDXI ReEe[ R[KYW R^VRX RTKUU RkD]F',
	    'PoRI[I RhLlL R`DhDhL RVDWG R_P`TeXl[ R^L\\M R]PjPhScX\\[ RTKUU RZKXW R\\VRX R`D_K',
	    'PoRI[I R]IkI RbYkY R_YhY RWCWI RhSl[ R_R[[ R`M]X ReMaY RYKXV R\\URV RTKUT RaCaG^U',
	    'PoTFjF RRMlM RVWaW Rb[g[ RVQVY R_C_G RVQaQaW RgMg[ RXHZL ReHdL',
	    'PoRI[I Rc[k[ RWDWI RdFd[ RlWk[ RiD]G RZKXW R\\VRX RTKUU RjK^M RhR^S RkQaS',
	    'PoRI\\I R]NlN RhDh[ RVDWG ReD]G RTKUU RZKXW R\\VRW RaFaQ`W',
	    'Po`EgE RRI[I Rc[h[ RWCWI R`V[[ RhFkM R`G]N RTKUT RZKXV RbN`W R\\URV R^NiNiVh[',
	    'PoTTkT RR[l[ RTETO RZDZQ R_Q_T RYUZY R_EaJeNlQ RfUc[ R]EiEhIcM\\P',
	    'PoRI]I RbSeS RXDXI ReCeR RiHlO RaH^O RjPgVdY_[ R]URX R[KYW RTLUT',
	    'PoRI\\I R`UgU Rf[j[ R`M`V R`MgMgT RVDWG RbC]M RTKUU RZKXW R\\VRW R`HlHlTj[',
	    'PoRI]I RdJlJ R_ZkZ RXDXI R_Q_[ RdCdQ R_QkQk[ R[KYV R]URW RTKUT',
	    'PoRI[I R]NlN R`[d[ RWDWI R]G]K RdNd[ R]GlGlK RdCdG RZKXW R\\VRX RTKUU',
	    'PoRI[I R`LhL RWCWI RbPb[ RdDlL RcE\\L R]PjPiXfX RTKUT RZKXV R\\URV',
	    'PoTFjF RRKlK RVShS RVWhW R_[k[ RVNVX R_C_F R`N`[ RVNhNiW RYGZJ ReGdJ RlXk[',
	    'PoRI]I R_IlI R][l[ RXCXI ReCeI R^WRZ RTLUV RiLf[ R[LYX RaLcX',
	    'PoTFjF RRKlK RWShS Rb[k[ RWNWS RcSc[ RWNhNhS RkWk[ RYHZK R_C_F ReGdK R\\TYYR[',
	    'PoTFjF RRJlJ RVQhQ RVUhU Rb[k[ RVMVU R_C_F RcUcZ RVMhMhU RYGZI RlWk[ RdGdJ R[UYYR[',
	    'PoTFjF RRKlK RVQhQ RVThT RRWlW RVMVT R_C_F R_T_[ RVMhMhT ReFcK RYF[K',
	    'Po]ElE RRI[I R_RkR R_ZkZ RWCWI R_K_[ R_KkKk[ RdEcK RZKXW R\\VRX RTKUU',
	    'PoRI[I R]JkJ R_OjO R\\SlS RWCWI RgFlK RdRfWiZl[ RbC_J R`K]Q RcU`Y[[ RYKXV R\\URV RTKUT RdNcV',
	    'PoRI[I RaRiR RWDWI RgIfNkNlL RgFlJ RaP]U RaSeXl[ RiSdX][ R[URV RSKTT RYKXV RbD`IjI RcIaM^N',
	    'PoRI[I R]MlM R^G^J R^GlGlJ RcCdG RUDWG RiUl[ RfPaZ RbO]Y RcI_V R[VRW RSKUU RYKWW RjXaZ',
	    'PoTFjF RRJlJ RVQiQ RVTiT RTXjX RR[l[ RVMVT R_C_F R_M_[ RVMiMiT RYGZI ReGdJ',
	    'Po\\GkG RRI\\I R]QkQ RWDWI R]K]Q RdCd[ R]KkKkQ RdR\\Y RZLXV R\\URW RTLUT RdRgUkZ',
	    'PoRI[I R_JiJ R_NiN Rd[i[ RWCWI R_F_S RcCcG R_FiFi[ RlNeU[Z RTKUT RYKXV R\\URV ReR]S RiRaS',
	    'PodFkF RRI[I R^NkN R^RkR R\\VlV RWCWI R^J^R RdCdJ RdRd[ R^JkJkR R[URW RTKUT RYKXV',
	    'PoRI[I R`PiP R`ViV R\\[l[ RWDWI R]G]J R`K`[ RdCdG R`KiKi[ R]GkGkJ R\\VRX RZLXX RTLUU',
	    'PoSE^E RSM^M RSP^P RTTjT RR[l[ RSESP RYEYI RYMYP RSH^H^M R_R_T RYU[Z RbFdKhOlQ ReUcZ RaEkEiJeN`P',
	    'PoRI[I R\\PlP R^UjU R_[c[ RWDWI RcLc[ RcGdJ R^LjLjV RhFkI R_E\\K RYKXV R[URW RTLUT RiC_E',
	    'PoRI[I R^MkM RbWjW R_WgW Ri[l[ RWDWI R]Q][ R^H^M R]EeEeV R^HkHkM R]QlQl[ RhSjX RYKXV R\\URV RTLUT RkCbE',
	    'Po^HkH RRI[I R^LkL R^XiX Rf[j[ RWDWI R^Q^Y R^D^L R^DkDkL RcTgU RdS`V RTLUT RYKXV RaL`O\\TRV R_PlPlUk[',
	    'PoRIZI R]IlI R_OlO R_TlT RiMi[ R]DlDlI RUDWG RiKhO R`Z^[ RZURW RSKUU RYKWW R]D]R\\Y RcLcOcTbX',
	    'PoRI[I R^JkJ R\\MlM RWDWI R]Q][ R^E^J RbQb[ RdMcQ RdCdJ RgQg[ RkEkJ RYKXV R[URW RTKUT R]QkQk[j[',
	    'Po]DlD RRI[I R`LkL R`QkQ R`UkU R^ZlZ RWDWI R`H`Q ReHeZ R`HkHkQ R[URW RSKUT RYKXV R]D]R\\Y',
	    'Po]FlF RRI[I R^LjL R^OjO R]RkR R]UlU Rd[h[ RWDWI R^I^O RdCdR RhSh[ R^IjIkO R`VbY RYKXV R[URV RTLUT',
	    'PoRI[I R]OlO R_VkV R_ZkZ R]G]O R_R_[ ReGeO R_RkRk[ R]GlGlO R`CaF RiIhM RiDfG RVDWG R`IaM R[VRX RYKXW RTKUU',
	    'PoRI[I R`LgL R^TiT R[[l[ R^O^T R^OiOiT RgIlN RiEfH RkHhJ R]G`J\\N RUDWG R`VaZ ReDhK RgVe[ R]EbE_L R[WRX RSLUV RYKWX',
	    'PoRI[I RfMjM R^PjP R^RjR R\\UlU Rc[g[ RVDVI R^J^R RbGbM R`GgGfM RgSg[ R^JjJjR R_WbY RbDgDlH R`E\\I R[TRW RSKTT RYKXV',
	    'Po\\ElE ReHlH RRIZI ReMlM R]McM R`UiU Rh[k[ R]H]L R^O^[ R]HcHcL ReUeZ ReJeL RiFiN R^OkOk[ RgQeU RUDVG RbQcT RZVRW RSKUU RYKWW',
	    'PoSF]F R`FkF RRJ^J R_JlJ Rg[l[ RUGVJ RXCXF RTM\\M\\R ReCeF RaMaRiSg[ RaMjMjR RlWl[ RbGcJ RhFhI R[GZJ RUSUZS[ R[SYY^X RdSbX_[ RTMTS\\S',
	    'PoSF]F RRJ^J R`MkM RTP\\P R_QkQ RTS\\S RRW]W RTMTS RXSX[ RTM\\M\\S R_N`P ReKeR RXCYF RbS_V R[GZJ RcE_G RUGUJ RaUcXj[ RbFjEgH`K RaTiTfX_[ RcGgIkJ',
	    'PoSF^F R`FkF RRJ^J R`JlJ RTP\\P RaPjP Rg[l[ RXCXF RTMTS[SZY RTM]M]S ReCeF RaMaSiSh[ RaMjMjS RlWl[ RbW_[ RUGUJ RbGbJ R[G[J RhFhJ RTZR[ R^XYX RVSUZ RcSaY',
	    'PoUJ_J RaJlJ Rb[g[ RYIY[ RgIg[ RWDRO RbE^O',
	    'PoUH_H RbHlH RVPjP RS[l[ RXGZL RfHhM RWDRM RbD`L',
	    'PoUG^G R`GlG Ra[h[ RdGgJ RXGZK RWCSL RbC^J R]UYYS[ R^L\\V RTOiOiTh[',
	    'PoUF^F RbFlF Re[k[ RYMeMe[ RlWk[ RYH[J ReHhJ RWWS[ RYQbV RVCSJ RcC`J RYMYRXW',
	    'PoUG^G R`GlG RULjL RRRlR RZ[_[ R_L_[ RdFfJ RXFYJ RVCSJ RaC_I',
	    'PoUG^G R`GlG RdUhU RX[j[ RXKXZ R`J`W RiLiT RcFfI RaF]J RXFYJ RkWj[ RVCSJ RaC_I RiLRQ',
	    'PoUG^G R`GlG RULjL RRSlS R_L_[ RdFfJ RXFYJ RVCSJ RaC^J',
	    'PoVH^H RaHlH RTOkO RW[j[ RWOW[ R]LaO RfGhL RYGZL RWDSK RbD_K',
	    'PoUG_G RaGkG RUOjO RR[l[ R_O_[ RYIZL RdIgL RVCSK RbD_K',
	    'PoUG^G R`GlG Rg[l[ RTNgNh[ RlWl[ RXGYK RdGeK RWQbW RVCSK RaC^J R]TYYR[ R^K\\U',
	    'PoUG^G RaGlG RSWgW Rd[i[ ReFfJ RVFRI RXFYJ RVCTH RbC_I RYNXRkRj[ RVKgKfR',
	    'PoUG^G RaGlG RTLjL RSSlS ReLe[ ReFhI RZVUZ RVFRJ RYFZJ RWCUH RbC_J R[L[RZV',
	    'PoUG^G R`GlG RdFfI RUFRI RYFYJ RVCTH RaC_H RXLhQ RdK]OUQ ReRcU\\XR[ RXR[U`XdZl[',
	    'PoUG^G RaGlG RVSiS RV[k[ RVLVZ R_L_S RVLiLiT ReFgJ RXFZJ RlWj[ RVCSK RbC_J',
	    'PoUG^G R`GlG RSNlN R_K_[ RXFYK RdFfK R_QgT RVCSK RaC^J',
	    'PoUG^G R`FlF RcFfI RWFYJ RVCSJ RZQ`WeYl[ RTKdKcQ RXQiQdW_YX[ RaD^J RYKWTUXR[',
	    'PoUG_G R`GlG Rb[h[ RdFfJ RWMRR RYFZJ RVCSJ RXJUP RaC^J RZQcS RfUVX RVNjNjTi[',
	    'PoUG^G R`GkG R]McM RVN_M_[ RXFYJ RhTk[ RdFfJ RVCSJ RaC^J RgLiX RVMVSUX RiL_M',
	    'PoUF^F R`FkF RRPlP RUUiU RVKiKiV RdFfH RXFYI RVCSI RaC_H R_K^TZXT[',
	    'PoUG^G R`GlG RTLkL RRRlR R_[d[ RYLYR RdLd[ RdFeJ RUFRI RaE]I RXFYJ RVCTH RaTYXRZ',
	    'PoUG^G RaGlG Re[j[ RTMT[ RTMjMj[ RaRfW ReFhI RVERI RXFZI RbC_I R_PZUVW R_J^R',
	    'PoUG^G R`GlG RULiL RRQlQ Rc[k[ RcQc[ RXFYI RdFeI RlXk[ RUFRI RaE]I RVCTH R[RYWWYR[',
	    'PoVG_G RaGlG Rc[h[ RfFgJ R\\RUY RaU\\[ RXLRR RZF[J RWCTJ R^M[S RcC_J ReM`X RWNkMi[',
	    'PoUG^G R`GlG RSNlN Re[l[ RYQeQe[ RlWl[ ReFfJ RaE]I RYFZJ R_J`M RVCTI RYQYWVYS[',
	    'PoUG_G RaGlG RRRlR RdGfJ RXGYJ R_ReXk[ RVCSJ RbC^J RiK_MUN R`M^TZXS[',
	    'PoUG^G R`GlG RYPeP ReFfK RXFZJ RaLlQ RUTgTd[ RVCSJ RbC`H R^J[MRQ',
	    'PoUG^G R`GlG RVR^R Ra[k[ RVMV[ RbMbZ RdGfK RlWl[ RWGYK RVCRL RaC^J RjPaS R]YUZ',
	    'PoUG^G RaGlG RbPgP R^QeQ ReUlU RaUgU R^VeV RZVaV RWV^V RSWYW R_[k[ R_L_[ ReFfI RkXk[ RXFZJ RVCSJ RaC^J R[MUM RgKYM R[QTR R^QWQ R`QZQ',
	    'PoUG^G RaGlG Ra[k[ RWKWU RbTbZ RWKhKhU RdFgI RlXl[ RbE^I RYFZI RVCSI R_T[XT[ R_O^U',
	    'PoUG^G R`GlG RVLVY RfUl[ R\\MaQ ReFfJ RYFYJ RVCSJ RaC^I R]UUY RhOeUbY\\[ RhLfU',
	    'PoUF^F RaFkF RdFgH RVERI RXFZI R[TcV RbC_I R[KTO RZL\\NbQkT RXXd[ RZLfKcN]QST',
	    'PoUG^G R`GlG R_MkM RVZhZ RVRV[ R_H_R RVRhRh[ RdGfK RWGYK RVCSK RaC^J',
	    'PoUG_G RaGlG RWOjO RVUjU RS[l[ R_K_[ RdFfJ RXFYJ RVCSJ RbC_J RXLVPTS',
	    'PoUF_F RaFlF RUPiP RUUiU RULUV R_L_[ RULiLiU RXGZI RdGfJ RVCTH RbC_J',
	    'PoUG_G R`GlG RVTjT RVZjZ RVNV[ R_K_Y RVNjNj[ RdGfK RXGZJ RVCSK RaC_J',
	    'PoUG_G RaGlG RWQiQ RUZjZ RUSUZ RWLWQ R_J_Z RiLiQ RjSj[ ReFfJ RUFRI RbE]I RVCTH RYFYJ',
	    'PoUG^G RbGlG RYOdO RTZkZ RTKT[ RTKkKk[ RYH[J RfHgJ RZRdW RVCTI RcCaI RdP`UXX',
	    'PoUG^G RaGlG RSPjP RVZiZ RVTV[ RVTiTi[ RdLhNkQ RdFfJ R\\KWP RXFXJ RVCSJ RaC^J',
	    'PoUG^G R`GlG RcFfI RWFYJ RcTf[ RhTl[ RVCSJ RVLVTR[ RaC^I RgLiX RVNUX RiJ^L RVM[M^L^YWY RcX\\Y ReX_Y',
	    'PoUH_H RaHlH RTOkO RS[l[ R_L_O ReGgL RXGZL RVCSK RbC_J RYQ[Y ReQc[',
	    'PoUG^G R`GlG RWQhQ RWVhV RR[l[ RWLW[ RWLhLh[ RdFeJ R`F]J RXFYJ RVCSJ RaC_I',
	    'PoUG^G RaGlG RUPjP RUTlT RUKU[ RXFYI RUKjKjO RdFgI RVERI R`QbVeYi[ RbC_J R_XTZ RlXk[h[',
	    'PoUG^G R`GlG RWUbU RdVhV RX[k[ RXPXZ RWPbPbU RWFZI RcFfI RVNRR RlWk[ RVCSJ RXJUP RaC_H RWMiMiQhV',
	    'PoUG^G R`GlG RbQhQ RVZiZ RVTV[ RVTiTi[ RWFZI RdFfI RVCSI RaC_I RTKiKhQ R^L[PWQSS',
	    'PoUG^G RaGlG RROeO RVXbX Rc[h[ RVRVY RVRbRbX RUKhKh[ RfGgJ RXGZJ RbC_J RWDUGRJ',
	    'PoUG^G R`GlG RZOlO Rb[g[ RgKg[ RcFfI R^S`W RYGYJ RVCSJ RaC^J RXLWNRS RWNW[',
	    'PoUF_F R`FlF RSRlR Re[i[ RVKV[ RXFYI R\\K\\[ RcKc[ RVKiKi[ RdFfI RUFRI RaE]I RVCTH',
	    'PoUG^G R`GkG RRNkN RXWfW R_K_[ RdFeJ RXFYJ R`NhUlW RVCRJ RaC^J R_MVVRW',
	    'PoUG^G R`GlG RdPjP RaPhP RWOW[ ReLhM RdFeJ RlWk[ RWORT RXFXJ R[QaPdWfZj[ RVCSJ RXLVQ RaC^I R`LcV RdP^P',
	    'PoUG^G RaGlG RWQhQ RWWhW RS[l[ RWLWW RWLhLhW RcGfJ RYGZJ RWCTI RbC_J',
	    'PoUG^G R`GlG ReYjY R_I_[ RcFfI RaE]I RXFYI RVCSI R_Q[VRZ RVMUSkRjY R]LVM RhJ[L',
	    'PoUF^F R`FlF RVPiP ReZiZ RWFYI R_K_[ RTKiKiO RdFeI RaE]I RVCSI R_S\\VRZ RUTkTiZ RUOUU',
	    'PoUG^G R`GkG RZPdP RcYhY R]S][ RUShShY RdFeJ RWFYJ R`KlQ RVCSJ RaC_H R^J\\MRQ',
	    'PoUG^G R`GlG RYMlM R^QjQ R^UjU R^L^[ RWFXI RdFfJ RZIYMTR RVCSJ RaC_J',
	    'PoUG^G R`GlG RSN^N RdTjZ RXFYI RdFeJ RaNeW RUT\\X^Y RVCSI RYKTU RaC_I R]P[VXYS[ R_MkMgTcY^[',
	    'PoUF^F R`FlF RUQiQ RfZjZ RWFXI RcJc[ RTLiLiP RcFfH RaE]I RVCSI R[J[VXYS[ RTUkUjY RUPTU',
	    'PoUG_G RaGlG RXWbW Rb[h[ RXQXY RXQbQbW ReFfJ RXMRR RXFYJ RVCTI RXJVO RbC_I RWMjMjTi[',
	    'PoUF^F R`FlF RYOhO RUOdO RRTlT RePe[ RXFYH ReLiP RdFgH RaC`H RVCSH R\\JZLUN RYVWYR[ RYQYW',
	    'PoUG^G RaGlG RbZkZ RX[][ RbMb[ RbMkMk[ RdFgI RXFYJ RVCSJ RbC_I RTZR[ RXKWW RSN^N^T]Z',
	    'PoUG_G R`GlG RSLlL RVW`W Rb[g[ RVPVX RVP`P`W RhLh[ RdFeJ RaF]J RXFYJ RVCSJ RaC_H',
	    'PoUG^G R`GlG RdTiT R^[k[ R^L^[ R^LjLjT RdFfJ RYUS[ RkWk[ RaF]I RWFYJ RULYM RVCSJ RaC_H RRQWQ',
	    'PoUG^G R`GlG RWNiN RSRlR RXGZK RdGfK R`TdXk[ RVCSJ RXJUP RaC^J R^T[XS[ R_M^U',
	    'PoUG_G RaGlG RfWjW RTLTV RbLb[ RbLjLkW RXF[H RdFgI RVERI RbC`H R\\N\\VYYT[ R\\TSU R]KTL',
	    'PoUG_G R`GlG RUKkK R[VgV RU[l[ RUKU[ R[O[V R[OgOgV RXFYJ RdFgJ RVCSJ RaC_I',
	    'PoUG_G R`GlG RROlO R_UgU RWZkZ RWKW[ R_J_U RgKgU RXGZJ RgGhJ RWCUGRJ RaCaG^J',
	    'PoUG]G R`GkG R`MhM R\\MdM R[RaR RdFfI RaE]I RXFYJ RaIeK RUNZN^N`VcYh[ RVCSJ R\\K_T RlXk[h[ RiTbW]YSZ R^RYRTS RhQcQ^R RkPeQ',
	    'PoUG_G R`GlG RSM^M RfXkX RYMYW RaLa[ RaLkLkX RcFgJ RXFZK RVCSK RbC_J R_VRW',
	    'PoVG^G RaGkG RROlO Ra[k[ RaOaZ RkXl[ ReFfJ RYFZJ RcKhM RWCTI RbC_J R\\OYVUYR[ RhQeTZY R]KZU',
	    'PoUG^G R`GlG RR[l[ RVMV[ R\\M\\[ RcMc[ RVMiMi[ RXGZK RdGfK RVCRL RaC^J',
	    'PoUG^G R`GkG R[NjN RcXiX R\\[l[ RXFYI RTJWN RdFeJ RUXR[ R`S\\X RVCSI RaC_H RRQWQWX[Za[ RjOiX RbJ`T',
	    'PoUG^G R`GlG RXZgZ RXTX[ RXTgTg[ RdFeJ RWFYJ RVCSJ RaC^J R\\LXNSP RZMfLdN^QTT RZNaQfSlT',
	    'PoUG_G R`GlG RVMiM RTRkR RUVjV RS[l[ R_S_[ R_J_R RdFfJ RXFYJ RVCSJ RaC_I',
	    'PoUG^G R`GlG RTSjS RTZjZ RTMT[ RWFYI R[J[Z RcJcZ RTMjMj[ RdFeJ RaE]I RVCSI',
	    'PoUG^G RaGlG R^[k[ R^L^[ RdJdW RjMjU RYHZJ ReHgJ RXUS[ RlXk[ RbC`H RTLXN RVCSJ RRQWQ RiMZQ',
	    'PoUG_G R`GlG RXPgP RUZjZ RUKU[ RUKjKj[ R_PgX RXFYI RdFgI RaE]I RVCSI R_S\\VXX R_M^T',
	    'PoUG^G RbGlG Rb[k[ RbKbZ RXG[J RfHhJ RkXk[ RbSjW RTNYP RVCTH RcCaI RhNbQ R[VXYS[ R\\K\\TRV R\\O[W',
	    'PoVF^F RaFkF RVMiM RRRlR Rb[k[ R_I_R RbRbZ ReFfI RlWk[ RWCUH RYFZH RWJUO RbC`H R[RYWWYR[',
	    'PoUF^F R`FlF RRNlN RUQiQ RTTjT RRXlX R_I_[ RVKiKiQ RXFYI RcFfH RVERI RbC]H',
	    'PoUG^G R`GlG RSM^M RXMXW RaLa[ RgRkU RdFfJ RaLkLfQ RXFZJ RVCSJ RaC_I R^URW RjXeY',
	    'PoUG^G R`GlG RRPlP RVZhZ RVTV[ RVThTh[ RWFYJ RdFfK RVCSJ RaC`G]J RgKUM R`L`T',
	    'PoUG^G R`GlG RULiL RRPlP RSTkT R`[e[ R_I_P RePe[ RYV\\Y ReGeI RaE]I RWGYI RUCSI',
	    'PoUG^G RaGkG RRMkM R^J_M RdFgI RYFZI RdOkR RWCTI RbC_J RZPSS RZR^VbYk[ RdR`V\\YR[',
	    'PoUF^F R`FlF RUP]P RUU]U Re[j[ RY[\\[ RUK]K][ RVCUGRI RaE]I RXGYI R_NkNkVj[ RUKUTTZ RfGdJ ReKdVa[',
	    'PoUG^G R`GkG RXRfR RWVhV RS[k[ RXFXJ R_R_[ RdFfJ RaE]I RWK`KcOlR RVCSI R^KZORS',
	    'PoUG^G R`GlG RWTbT RWXbX Rc[h[ RWPWZ RWPbPbX RXFYI RdFeI RVNRR RaE]I RVCSI RXJUP RWMjMjSi[',
	    'PoUG^G R`GlG RSN^N RbYkY RbMb[ RbMkMk[ RcFfI RaF]J RTT]X RVCSJ RXFYJSU RaC_H R]O[VXYR[',
	    'PoUF^F RaFkF RXNX[ ReGfI RYLRR RXGYI RVCSI RbC_I RiQeV[[ RdKiL R[PlO R`KcUdYk\\lX',
	    'PoUF^F R`FlF RUKkK RYNhN RZRgR RXWiW RUZlZ RUKU[ RWFXI R`N`W RcFfI RaE]I RVCSI',
	    'PoUG^G RaGkG RRM\\M Rh[l[ RWMWV R_LhLhZ RlXl[ RbQeU ReGfJ RYGZK RWCTJ RbC_J R\\URW R_L_S^WZ[',
	    'PoUG_G RaGlG RYNfN RZWeW Re[j[ RUKU[ RZRZY RZReReW RUKjKj[ RXFYI RdFgI RVCSJ RaC_I',
	    'PoUG^G R`GlG RSL^L R`LkL R_SlS ReLe[ RdFeJ RXVT[ R`F\\J RWFYJ RVCRJ RaC_I RYLYTWY R^RRT RYPXW',
	    'PoUF^F R_FkF RYPeP RWZgZ RWTW[ RWTgTg[ RdFeJ RWFYJ RVCSI RaC^I R^KZORR R^KdNmQ',
	    'PoVG^G RaGkG R^KkK R\\QlQ Rb[g[ RXQX[ RgQg[ RYFYH RdFgI RWLSN RXJUM RYOTT RWCTI RbC_J',
	    'PoUG^G R`GkG RSKkK RfUiU RUOUU R_J_[ RUOiOiU RaE]I RXGYI R_RbVlZ ReGeI R_R[VRZ RVDUGRI',
	    'PoUG_G R`GlG R[UkU RWNW[ R[N[V RcKc[ R[NkNkV RdFfJ RVORT RaF]J RXFYJ RVCSJ RXKUQ RaC_H',
	    'PoUG^G RaGlG RSN]N R`YjY RU[X[ RXKX[ R`M`Z R`MjMjZ ReFfJ RXFZJ RVCSJ RbC_I R\\SSU',
	    'PoUG^G R`GlG RTLkL RVOiO RSSkS R_I_[ RXFYI RcFfI RaE]I R_RcVkZ RVCSH R_R\\VSZ',
	    'PoUF^F R`FlF RUM^M RaRkR RRVlV RUJUR R_R_[ RaJaQ RYFZI RdFfI RkNkR RaC_H RVCSI RiK`M R^QTR',
	    'PoUF^F RaFlF R\\JlJ RgXjX RTLTS R^N^X RdJd[ R^NjNkX RYFYI RdFgH RVERI RbE^H RVZTZ RYJYRXX',
	    'PoUG_G RaGlG RVUiU RTZiZ R_K_Q RTQiQi[ RWKYO RYFZI ReFfI RVERI RgKfO RbC`I',
	    'PoUF^F R`FkF RRRlR RVVhV RZ[_[ R_N_[ RWNhNhW RcFfH RYKcJ_N RWDRH RaD]H RXFYHWLSN',
	    'PoUG^G RaGlG RSRkR RTLTO R_N_[ RTLkLkO RYFZI R^IaL RdFgI RWDRI R_QdUlY RbC`H R^RZVSY',
	    'PoUF_F R`FlF R]KcK RSUlU RZ[_[ R_S_[ RXFYI RWL_L_O RgLkO RcFfH RWLSP RaE]I RVCTH RWQgQ_S RgJ`K',
	    'PoUF^F R`FlF RRO[O R^QeQ Rd[i[ RWJWW RcFfH RaE]I RXFYI RVCSI R_J\\Q R[VRX RfV\\W R^MkMkSi[',
	    'PoUF^F R`FkF RYOeO RTSkS R[[_[ R_O_[ ReVj[ RXFZI RcFfI RYVT[ R_IbLlP RVCSI RaE^H\\LRP',
	    'PoUG^G RaGlG RSN\\N R^SkS R_[k[ RT[X[ RXKX[ R_K_Z ReKeS R^KkKkT RcFfI RlXl[ RYFYJ RVCSJ RbC_I R\\SRU',
	    'PoUG^G RbGlG RSP`P Re[j[ RZLZ[ RcLcW RjKj[ R]T_V RYS]T RYH[J RfHiJ RVFRI RWTRY RVCTH RYOWU RcCaH R^KSM',
	    'PoUG^G R`GlG RWOhO RWShS RWKWZ RWKhKhS RcFfI RaC`G]I RWFXI RUCSI RjTcW R^ScXgZl[ R[ZT[ R_YWZ RaYZZ',
	    'PoUF^F R`FlF RWPhP RUZjZ RUTU[ RWKWP RWKhKhP RUTjTj[ RcFfI RaE]I RWFYI RVCSI R_P]T',
	    'PoUF^F R`FlF RWOgO RRSlS Rc[k[ RSKSN R_I_K RcSc[ RSKkKjN RkXk[ RWFXI RcFfH RaE]H RVCSI R\\SZWXZR[',
	    'PoUF^F R`FlF RVNhN RVRhR RVUhU Rb[l[ RVKVU RcUc[ RVKhKhU RcFfH RlXl[ RaE]I RWFYI RUCSI RYYR[',
	    'PoUG^G R`GkG RVZhZ RVTV[ RVThTh[ R^LaO RYFYI RdFgI RVPgPeT RWERI R^JjO RaC_JRO',
	    'PoUF_F R`FlF RUSjS RUWjW Re[j[ RUOU[ R_O_[ RUOjOj[ RWFXH RdFfH RUERH RbD]H RVJiK_M R[MhO',
	    'PoUG^G R`GlG RTJkJ RYQgQ RYUgU RTZlZ RTJT[ RWFXI RYNYV R`N`X RYNgNhV RcFfI RaE]I RUCSI',
	    'PoUG^G R`GlG RVOhO RVShS RVWhW RVLVW RVLhLiW RcFfI RaF]J RXFYJ RVCSJ RaC_H RcYl[ R[YS[',
	    'PoUG^G R`GlG RUMkM RXX\\X RWFYI RlXk[ RaE]I RdFdJhK RaPdWgZj[ RVCSI RiOgSbX\\[ R`JbU RUMUTTY RUQ]Q\\X',
	    'PoUF^F R`FlF RSJkJ RUNiN RRRlR RRVlV R_J_R ReSe[ RXFYI RcFfH RVERI RbC]H RYWWYS[ RYSYX',
	    'PoUG^G RaGlG RTMjM RR[l[ R_M_[ RXS]W ReSjX RdTaX RWUSX RYFZJ ReFgK RfOcW RWCTJ RYOUW RbC_J',
	    'PoUG^G R`GlG RUP]P RUT]T Rf[i[ RUKUY RXFYI RcFfI RbV^[ RaE]I RVCSI R_NkNkUj[ RdKcW RUK]K^XRZ',
	    'PoUF^F R`FlF R^NlN RcQkQ RcVkV RWMW[ RXFYI RcMc[ RdFeI RXJWMRR RaE]I R^MZQ RVCTI R_J]P',
	    'PoUF^F R`FlF R`NlN RWLW[ R[M[X RdFfH RVMRR RXFYI R`NbTfXl[ RVCTI RXJUO RaC_H RaI]R RiMfTbX[[',
	    'PoUG^G R`GlG RWRgR RWVgV Rc[g[ RWMW[ R_I_N RVNgNg[ RXFYI RcFeI RVNRP RYJUO RaE]I RdKjM RVCSI',
	    'PoUG^G R`GlG R\\PlP R]WkW R][l[ RdKdW RXFYI RcFfI RXVS[ RaE]I RVCSH RSJYJVNUQZQVX RUTZYc[ RiJ^K',
	    'PoUF^F R`FlF RRLlL RWFXI RWP[T RfQhRkU RcFfH RaE]I RWQSU RfNdS RVCSI RXNUT R_P]UZXR[ R_I_QaVeYl[ R_L^U',
	    'PoUF^F R`FlF RdQkQ R^WkW RWFXI R^O^V RdKdW RdFeI RaE]I RUCSI RjJ^L RRLXLURZRXWT[ RUUYYb[l[',
	    'PoUG^G RaGlG RVMiM RRQlQ RVZhZ RVTV[ R_J_Q RVThTi[ RXF[H RdFgI RXJTO RVCSI RbC`H',
	    'PoUF]F R`FkF R[RlR RWJW[ R\\LiLiR RcFfI RYLZO RXFYI RbJbReVgYl[ RVCSI RaC_H RaU^YZ[ RTMSR RbNaV',
	    'PoUF^F RaFlF RVNhN RVQhQ RUTiT RSWlW RVJVQ R_T_[ RVJhJhQ RXGZI RfGhI RVERI RbCaG',
	    'PoUF^F R`FlF RUKjK RUNiN RRQlQ RSTlT Ra[f[ RfQf[ R[V_X RWFYH RcFfI RaE^H RVCTH R_K[TVXRZ',
	    'PoUF_F RaFlF RUJkJ RYNiN RZRhR RXWjW RUZlZ RUJU[ RaNaV RdShU ReFfI RVERI RbD^H RXFYI',
	    'PoUF^F RaFkF RRJlJ RUQiQ RUTiT Rb[l[ RUMUT RUMiMiT RZFZI RdFgH RWDSH RbC`H R_J_U\\XYYR[ RWW[Y]Ze[',
	    'PoUF^F RaFlF RWNgN RUPUW RWJWN RWJgJgN RUPiPiW RYFZH ReFgH RVDRI RbC`I R_S^W RaWl[ R]XS[',
	    'PoTF^F R`FlF R[OhO RRUlU R[J[O R[JhJiO RcFfH RUCTH RaD]H RUJUQhQhU R`UcYk[ R_R]V[XXZR[',
	    'PoUG^G R`GlG RZPeP RR[k[ ReFfJ RdWc[ RWSYY R^R`X RXFYJ R`KlP RgSdY RVCSJ RaC_H\\LRQ',
	    'PoUF^F R`FlF RSTlT R_Q_[ RcFfI RVERI RaC_H RaUlZ RVCTH R\\IXMTO R]VSZ RZKeKcM^PTR R[MbPkR',
	    'PoUG_G RaGlG RZSeS RZXeX Rf[k[ RTMT[ RZOZX RZOeOeX R[LkLk[ RVKXM RYFZI ReFhI RVCTH RbC`H',
	    'PoUF^F R`FlF RTKjK RRQlQ RWZhZ RWTW[ RWThTh[ RXFZI R_I`K RdFfI RaE]I RYLZP RVCSH ReLcQ',
	    'PoUF^F RbFlF RSLkL R_I_[ RXP[S RYH[J RfPkT RfHiJ RUFRI R_QeWlZ RfNdS RVCTH RXNVS RcCaH R_QYXSZ',
	    'PoUG^G R`GkG RUO]O RUS]S RULUX R]L][ RdTjZ RdFfJ RhSaZ RXFZJ RaMeV RRL_LjMfV RVCSI RaC^J R_VSX',
	    'PoVF_F RbFlF RUOiO RUSiS RRWlW RUKUR RbSb[ RUKiKiR RYFZI ReFhI RVCTI RcCaH R_P[TWV R_K^R',
	    'PoUF^F R`FlF RVNiN RVRiR RRTlT RVJVQ R_J_Q ReTe[ RVJiJiQ RcFfH RUERH RaE^H RXFXH RXYS[ RYTXY',
	    'PoUG^G R`GlG RVT^T Ra[k[ RUKUP RVRVZ RaRa[ RUKjKjP RWFYI RdFeJ RlXl[ RaE]I RVCSI R_MYP R^MgP RjSaU RYZR[ R]ZVZ',
	    'PoUG^G R`GlG RXNfN RZWdW RTZjZ RTJT[ RZRZW R_L_R RZRdRdW RTJjJj[ RWGXI RdGfI RaE]I RUCSI',
	    'PoUG^G RaGlG R_QjQ R[QgQ R\\ZjZ R\\T\\[ R\\TjTj[ RTKXL RgMlR ReFhI RYTSZ RVERI RaK[Q RXFZI RROVQ RbC`I',
	    'PoUG^G R`GlG RRN\\N R_QjQ RT[W[ Rf[i[ RWJW[ R_K_P R_KjKjP RXFXI RcFfI RaV\\[ RaE]I RUCSI RbQ`X RaUjTi[ R\\RRT',
	    'PoUG^G R`GkG RRL^L RXIYL RYFZI ReJlQ RdFfI RdIcM^Q RbQhS R`VjZ RUCSI RaC_I R[[W[ RVMTY RVQ\\Q\\U[[',
	    'PoUF^F R`FlF RXFXH RgFfH RUCTH RaD]H R`IdI R`QeR RfM_OVQ RfW_YU[ RULjL RTUkT R^TaYj[mY R_KaOdPjQlO',
	    'PoUG_G RaGlG RSKaK RTQ`Q RTU`U Rf[j[ RdLdW RjJj[ R]NaQ RXFZI RdFgI RVERI RYKTQ RbC`H RZR[ZT[ R_YVZ RaYZZ',
	    'PoUF^F R`FlF R]KkK RRN[N R]ZlZ R]K][ R`O`V ReMeX RdFeI RaE]I RWFXI RVCSI RWJW[S[ R`OjOjVhU R[SRT',
	    'PoUF_F R`FlF R_JjJ R_PfP RZQaQ RXQ^Q R_SkS Rc[k[ R`N`S RcUcZ RkXk[ RcFfH RVERI RUMkMjP R\\U[YV[ RbC^I_M RUMUTTY',
	    'PoUF^F R`FkF RRRlR RVVhV RZ[_[ R_N_[ RWOhOhW RXFYH RgJkO RgFfI RVDRH RWKSO RaD]H ReJ^KWK R`K_N',
	    'PoUF^F R`FlF RTJjJ RULjL RROlO RVVhV Rc[h[ RVRV[ R_I_O RVRhRh[ RWFYH RcFfH RUCTH RaD^H R_R_V',
	    'PoUF^F R`FlF RSKkK RRQlQ RVThT R_WjW R][l[ R_I_Z RVNhNhT RXFYI RcFfH RXVS[ RVERI RbC]H RYYc[',
	    'PoUF^F R`FlF RRLlL RVOhO RdZhZ RSRSU RVUVZ R_R_[ RVUhUhZ RVJhJhO RSRkRkU RWFXH RcFfH RUERH RaD]H',
	    'PoUF^F R`FlF RXNfN RSQlQ RZ[_[ R_Q_[ RcFfH RaE]I R_RdWkZ RVS[U RVCSI RYJWO RYKfKeQ RhTcV R\\WSZ',
	    'PoUG^G R`GlG R]SkS R]ZkZ R]M][ R]MkMk[ RdFfJ RYUS[ RcKbM RXFZJ RULYN RVCSJ RaC_J RSQXR',
	    'PoUF^F R`FlF RTLkL RXOfO RXSfS RRVlV RXJXV RfJfV RXFYI RcFeI RaE]I RcWk[ RVCSI R[WS[',
	    'PoUG^G R`GlG RRN^N R_NlN RYJY[ ReJe[ RZQ]T RYP[S RXFYI RhSlW RcFfI RbS]X RaE]I RUTRW ReLiT RVCSI RXMUU ReLaU',
	    'PoUF^F R`FlF RVLiL RVOiO RVRiR RRVlV RVIVR ReRe[ RVIiIiR RdFfH RUCTH RaD^H RYF[H R[SZWXYS[',
	    'PoUG^G R`GlG RWQgQ RTZjZ RTKT[ RWFXI RXN`N`X RTKjKj[ RcFfI RaE]I R_PdUgW RUCSI R_Q[UWW ReM\\N',
	    'PoUG^G R`FlF RUP\\P RfPjP RTT\\T RX[\\[ RUK\\K\\[ R`K`[ R`KjKjP RcFfI RaE]I RWFXI RbRcUgYl[ RVCSI R`RjRiVeYb[ RUKUTSZ',
	    'PoUG^G R`GlG RWP^P RUZ_Z Re[j[ RUTU[ RWFYI RUT_T_[ RcKcW RjJj[ R[K`P RcFfH RZJSR RaE]I RVCSI',
	    'PoUG^G R`FlF RT[W[ RXNX[ RcKcZ RXFYI RcFeH RSK\\KWO RVCSH RaC_H R\\QRS RiJiOjZ R^L^R]Y RjJ^K RfVhZ`Z',
	    'PoUG_G R`GlG RbQjQ RVUiU RS[l[ RTLTO R_I_L R_U_[ RcLcQ RTLkLkO ReFfJ RbD]I RXFYJ RVCTH R\\LZOYQTS',
	    'PoVF^F RaFlF R]MlM RRN[N R`SiS R`ZiZ RS[W[ RWJW[ R`J`[ RiJi[ ReFfI RYFZI RWCTH RbC`I R\\RRT',
	    'PoUG^G R`GkG RYMfM RSQlQ R`XdX RY[g[ RWFYI RYTY[ RYTdTdX RfJjN RdFgI RhXg[ RYJUO RaMgSlU RVCSI RaE]I\\NWTRV',
	    'PoUF^F R`FkF RVNiN RVRiR RVV^V Ra[l[ RVJVR RVSV[ RaSa[ RVJiJiR RWFXI RcFfH RlXk[ RaD]H RUCTH RjUaW R^ZXZR[ R\\ZU[',
	    'PoUG^G R`GlG RTNjN RRSlS RSWkW RS[l[ RXNXW RUK_K_[ RfNfW RWFXI RcFeH RaE]I RVCSI RiIaJ',
	    'PoUF^F R`FlF RWRgR RWZiZ RSKSO RWNW[ R_I_K RWNgNgR RWUiUi[ RSKkKkO RWFXH RcFfH RUERH RaD]H',
	    'PoUG^G RaGlG ReUiU Ra[k[ RSKSN RaOaZ RaOiOiU RSKkKkN R^HaK RdFgI RlXk[ RWERI RXMWPRT RYFZI RbC`H RWP^P[V RWT\\TXYS[',
	    'PoUF_F RbFlF RSMaM R]U`U RTPTU RZJZ[ RTP`P`U RdLdV RYG[I RfGiI RbE^I RZVaY RVCTI RZTTZRZ RjKj[f[',
	    'PoUG^G RaGlG RTLkL RRVlV R_T_[ RYP]Q R^J`L RdFgI ReN`S RVERI RYOUT RYFZJ RdPiR RbC`H',
	    'PoUF^F R`FlF R]QjQ RdJdQ R]MlMkP RdFfJ RXVS[ RaE]I RWFYI RUKZM R_Q`TdXk[ RSPXR RVCSJ RaC_H RiSeX][ R]M\\S[Y',
	    'PoaFlF RUF_F RTJjJ RVLiL RROlO RWRWW R`H`O RWRhRhW RXE[H RdEfH RVDRH RbC`H RaXk[ R_S^W R]YS[',
	    'PoUG^G RaGlG RUJlJ RXNiN RWTkT RU[l[ RUJU[ R[P\\S RYFZI RdFgH RfOeT RVCTH RbE^I R_ThX R`L`T]XWZ',
	    'Po`FkF RTF^F RRM[M R^SjS R\\WkW RT[W[ RWIW[ RdQd[ RXFZH RcFfH RVERH RaC_H R_JaMeOlP R]JjJgMbO]P R[RRT',
	    'PoUF_F R`FlF RTNkN RTJTN R\\J\\N RcJcN RTJkJkN RYFYI RdFgH RUERI RaE^H R[QTT RZRiReV`XXZ RZUaXS[',
	    'PoUF^F R`FlF RVPhP RVThT RRXlX RVLVT R_L_[ RVLhLhT RVIXK RWEXH RcEfH RhIdL RUCTH RaD^G R^J_O',
	    'PoaFlF RUF^F RRMlM RTPiP R_H_[ RiSi[ RUJiJiP RYFYH RVDRH RcTeY RbC`G R[TYY RUSUY',
	    'PoUG^G R`GlG RSLlL RRPlP RYZiZ RYSY[ RZIZN RdIdN RWUiUi[ RWFXI RcFfI RaE]I RVCSH R\\PWURW',
	    'PoUF^F R`FkF RRLlL Re[i[ RUOU[ RbObX RiOi[ RWIYK RWFZH RcFeH RgIeK RUCTH RaD]H RWQ[S RWU[W RUO]O][Y[',
	    'PoUF_F RaFlF RWNhN RWUgU RWXgX RR[l[ RSKSO RWQWX RWQgQgX RSKkKkO RYFZI R^I_K RdFgH RVCTH RbC`H',
	    'PoUF^F R`FlF R[JlJ R[PkP R[TkT RWLW[ R[M[T R[MkMkT RcFfH RVCTH RaD]H RWMRR RXFXIUO RcJcV`Y[[ RbZl[',
	    'PoUF^F R`FlF RWNbN RWPbP RdPhP RRWlW RZ[_[ RWLWP R_U_[ RWLbLbP RWFYH RcFfH RUERH RWISM RaD]H RWSfS_U RWJiJhP',
	    'PoUG^G RaGlG RRN^N R`QkQ R`UkU R`ZkZ RXKX[ R`L`[ R`LkLk[ RXP]U RdFfJ RXFYJ RVCSJ RXNTV RaC^J',
	    'PoUF^F R`FlF R]NjN RZRlR Re[k[ RcJcR RfRf[ RdFfJ RYUS[ RlWk[ RaE\\I RXFXJ RULYM RVCSJ R^K\\Q R_W]ZY[ RRQWR R`R^Y',
	    'PoUF^F R`FlF RUNjN RXRkR RWVlV ReRe[ RUJjJjN RcFfH R]WY[ RfNdR RaD]H RXFYH RVCTH RUJURTY R\\N^Q\\X',
	    'PoUF^F R`FlF RULlL RWO_O RXW_W RXRXX RWFXI RXR_R_W RdWi[ RdFgI RbQfY RdJiK RVCSI RaC_I RiOhRbY][ RlXk[i[ RaJdW RULUTSZ',
	    'PoUF^F R`FlF R[M_M RXThT RXWhW RXZhZ RXPX[ R_H_M RTQhQh[ RWFYH RhKkN RUCTH RaD]H RcFeIiK RYJVLSM RdM]OSQ',
	    'PoUG^G R`GlG RRN^N RaOjO RaTjT RTX]X Rf[j[ RTSTZ RXJXS RTS]S]X RaKjKj[ RWFXI RcFfI RaE]I RUCSI R^Z\\[ RaKaS`X',
	    'PoUF^F RaFlF R_LlL RRL\\L RhVjV Ra[k[ RVP[P[Y RaNa[ ReMeW RkPkV RWIWK RXFYH RdFgH RlXl[ RVERI RbC`H^N RZ[W[ RkP]R RVMTY',
	    'PoUF^F R`FlF RUKfK RROlO RYWiW RYZiZ RWFXI RYSY[ R^H^O RWTiTi[ RcFfH RaE]I RbQeR RUCSI RiJfM\\RRU',
	    'PoaFlF RUF^F RSOYO RST[T Rc[i[ RYFYI RbQeT RdFgH RVERI RXJSO RbD^H R[LST R\\V\\Z RXWX[ R[P]U RSVS[ RbI_P R`MkMkSj[',
	    'PoUG^G R`GkG RTKjK RUNiN RSQkQ RXWfW RXZfZ RXSX[ RWFYI RWTgSg[ RcFfI RaNfSlU RVCSI RaE^I\\OXSRV',
	    'PoUF_F R`FlF RZNlN R[QjQ R[UkU RZXlX RWPW[ RbIb[ R[KjKjQ RXFZI RcFfH RVESI RXJSN RbD]H RXNRT',
	    'PoUF^F R`FlF RUMjM RWPkP RXWjW RV[l[ RaUaZ RUJjJjM RWFYH RWTgSiU RcFfH R^PZT RUCTH RaD]H RdQhS RUJURTY',
	    'PoUG^G R`GlG RbJgJ RRMlM RVSiS RVViV RTXjX RR[l[ RVOVU RUK`J`[ RVOiOiU RcFfH RbD]I RXFXI RVCSI RdJ_J',
	    'PoUG^G R`GlG RUS`S RUV`V R\\[`[ Rf[j[ RUOU[ RZJZO RUO`O`[ RdKdV RkJk[ RWFZI R^KaO RcFfI RVLSO RUCSI RaC_H',
	    'PoTF^F R`FlF RVNhN RRPlP RWSgS RWVgV RXYkY RUYiY RRYgY RVJVN RWPWY RgPg[ RVJhJhN RcFfH RUCTH RaD]H',
	    'PoUG^G R`GlG RUO^O RUS^S RfXkX RUKUY RUK^K^S RbKb[ RbKkKkX RZU_Z RdGfI RaE]I RWGXI RUCSI R]XTZ',
	    'PoaGlG RUG^G RVOhO RVRhR RTUjU RUXiX RR[l[ RVKVR R_U_[ RVKhKhR RdFgH RVERI RbC`H R_I]K RXFYI',
	    'PoUG^G R`GlG RVNiN RVQiQ RVUjU RWXiX RR[l[ RVJVQ R_S_[ RVJiJiQ RWFYI RdFfJ RaE]I RWSSW RVCSJ',
	    'PoUF^F R`FlF RVNjN RZTiT RZWiW RZZiZ RZPZ[ R`J`N RZPiPi[ RVJjJjN RdFfH RUERH RbD]H RXFXH RVJVQTY',
	    'PoUF^F R`FlF RRK_K RSQ^Q RfTjT RSU^U RRX_X Rb[k[ RSNST RYJY[ RSN^N^T RbKb[ RaKkKkS RWFXH RcFfH RlWl[ RUERH RaD]H',
	    'PoUF^F R`FlF RUMjM RRPlP Rf[j[ RUSU[ RUIUM R\\S\\[ R_I_M RcSc[ RjIjM RUSjSj[ RXFYH RcFfH RVERH RaD]H R_P]S',
	    'PoUF^F R`FlF RXMfM RSPkP RcFfH RVCTH RbD]H RaPcTgXlZ RWFYHWN RYJgJeO RiRdT R`PTS R^STV R\\Q_TaWa[\\[ R`WRZ',
	    'PoUF^F R`FlF RUQjQ RWWkW Rg[j[ R^S^[ RdSd[ RUMjMjQ RWSkSk[ RWFXH RcFfH RUERH RbD]H RXTX[ RUJiJ RUJUQUVRY',
	    'PoUG^G R`GlG RSL^L R`MlM RRQ_Q RSU^U Rd[h[ RXIXQ RXRXZ RhIh[ RcRdV RWFZH RcFfI RaE]I RVCSI R^YSZ',
	    'PoUF^F R`FlF RRK\\K RhRlR RSTlT RWKWP R_R_[ R_JhJiQ RWFXH RcFfH RVCTH RaD]H RaMeO R_UjZ R_USZ RlOlR R[ORP R_K^O[R',
	    'PoUG^G R`GlG RYNlN RYTlT R^J^T RgJgT RXFYI RhXk[ ReWiX RcFfI R]YY[ RaE]I R_W[Z RTJXL RROVP RUCSI RVTS[',
	    'PoUF^F R`FlF RTJkJ RWMiM RTZlZ RTJT[ RZQ\\S RWFXH RfQiT RcFfH RUERH RaD]H R`ObTdViX RfOdS RZOXT R_S\\VWX R`M_T',
	    'PoUF^F R`FlF RZNlN R\\QjQ RYUlU RWLW[ RXFYH RcEfH RVCTH RaD]H RcVfYj[ RWITR R]O[T R\\JhJhN R`YY[ RbQbW',
	    'PoUG^G R`GlG RRP]P RYKY[ RfVk[ RWR\\V RXFYI RdFeJ RcV^[ RUGRJ RaE]I RVURX RdKdRaX RkNiS RVCTH RXOTW R`O^S RXMWS R\\KSL RdNeV',
	    'PoUG^G RaGlG RSPZP RSUZU RSYZY RSKS[ RSKZKZY RdJdU RXFYI RhMkQ RdFgI RVERI RbC_J R_M\\S RiSeXaZ\\[',
	    'PoUF^F R`FkF RRN_N RUS]S RUY]Y RUJU[ R]J]Z RgVl[ RcFfI RdV_[ RXFYI ReRhY ReRbX RbMlMjQ RUCSI RaC_H RcJ_R ReNfV',
	    'PoUG^G RaGlG R]KkK RRN\\N RhWjW R][l[ RXJX[ R]K][ RaOaW ReMeY RaOjOjV RWO\\T RXFYI RdFgI RWESI RXMSV RbC_J',
	    'PoaFlF RUF^F RUMhM RROlO RUJVM RVRVW R_I_O RVRhRhW RUJhJhM RYEYH RVDRH RaC`G R_T^X RaXk[ R]XT[',
	    'PoUF^F R`FkF R[McM RXSgS RXVgV RXPX[ RXPgPgU RXFYI RcFfH RWDRH RaD]H R`JkO R^JSP RhWbX R`WdYj[ R`Z[ZU[ R^ZW[',
	    'PoUF^F R`FlF RSNlN RRUlU RS[Y[ R_J_S RXJ[N RXFYH RdFfH RfJcN RaD]H RUCSH R_MePkR R_NYQSR RZXj[ R\\SWX`Y ReW`YU[',
	    'PoUF_F R`FlF RWMgM RVRiR RVUiU RVXiX RVOVX RWIWM RWIgIgM RVOiOiX RWFYH RdFfH RVERH RbD^H RcYl[ R[YS[',
	    'PoUF^F R`FlF RUKiK RROlO RR[l[ RVUV[ R\\U\\[ R_I_O RbUb[ RVUiUi[ RXFYH RVSgSjT RdFfH RVDRH RbD^H RdQhR R^OZPVS',
	    'PoUF^F R`FlF RUJlJ RVPlP RYSiS RYZiZ RYVY[ R`H`J R`K`T RYMiMiS RYViVi[ RWFXH RcFfH RUCTH RaD]H RUJUSTZ',
	    'PoUF^F R`FlF RVLjL RWNiN RWPiP RWSkS RWJWS R`J`S RWFXH RcFfH RUERH RXJSO RaD]H RSVhVcX[YbZk[ RcYT[',
	    'PoUF_F RbFlF RWNdN ReQiQ RWXdX Rd[i[ RWUWX RWKWN R^J]O RdKdN RdUdX RYFZH ReFgH RVDRH RXHSM RbCaG RXPRV R\\PTQ R\\YS[ RVSkSj[ R^S]Y RVJjJiP',
	    'PoUF^F R`FlF RSJlJ RXPhP RZYeY Rf[j[ RTST[ RXMXP RZVZZ RZVeVeY RXMhMhP RTSkSk[ RWFXH RcFfH RUERH RbD]H',
	    'PoUF^F R`FlF RTJkJ RcNjN RXQ^Q RcQiQ RcTkT RTZlZ RTJT[ RcKcY RXFYI RcFfH RWN^M]UYX RVERI RbC]H R]TVU',
	    'PoUF_F R`FlF RUO]O RbNjN RY[][ Rf[i[ RTJ]K]N RaJjKjN RdFfH RaE^I RXFYI RVCTH RXTTV R[UXXTZ ReT`V RhUeXaZ RaNaRkRj[ RTNTR^R][',
	    'PoUF^F R`FlF RWVgV R[X\\[ RhWk[ RaWc[ RcFfH RVWS[ RUCTH RaD]H RcMgRlT RSM[NVSRU RjLdO RWJfJ_M_S[T',
	    'PoUF^F R`FlF RTJjJ RUMjM RSPkP RSUkU RSXkX Rc[h[ RVRV[ RZHZO R_P_X RdHdO RVRhRh[ RWFXH RcFfH RUCTH RaD]H',
	    'PoUG^G RaGlG R]OjO R]SjS R[VjV RY[l[ R]K]S RcTc[ R]KjKjS RdFgI RYUS[ RYFYI RUKYL RRPWQ RVCTH RbC_J',
	    'PoUF^F R`FlF R[KkK R\\NjN RYQlQ R\\UjU R\\XjX Re[j[ RWMW[ R\\S\\[ RcJcP R\\SjSj[ RdFfH RVCTH RWNRS RXJVO RaC_H',
	    'PoUF_F R`GlG R_LlL R\\TlT RVLV[ RZLZX RdRd[ RXFYI RcFfI RaE]I RbI\\N RdThXlZ RVCTH RdT`X[Z R`LbOePlR RiKfObP\\R RXIUNRQ',
	    'PoUF^F R`FkF RVKhK RVNhN RVPhP RRSlS R\\ZfZ RZZdZ RWZaZ RVHVP RVHhHhP RdEfH R]T\\XYZ RVDRH RbC^G RdUlX RbWgY R\\PXURW',
	    'PoUE^E RaElE RUIiI RRLlL RVRhR RVThT RVWhW RVOVW R_I_L RVOhOhW RXFYH ReFgH RVCRG RbC_G RcYl[ R[YR[',
	    'PoTF^F R`FlF RSJkJ RURiR R`[f[ RSNSR RSNlNlR R_PaR RYKZM RWFZH RdFfH ReKdN RUERH RbD]H R\\R[WXYS[ R[VgVf[',
	    'PoUF^F R`FlF RVJjJ RVNiN RVQiQ Re[j[ R_J_T RXFYI RdVfX R_W`Z RcFfI RaE]I RYW[Z RVCSI RUWSZ RVJVTkTj[',
	    'PoUF^F R`FlF RTJjJ RURiR RRUkU RUMUR R[J[R R_R_[ RbJbR RUMiMiR RWFXH RcFfH RVCTH RaD]H R_UTZ R_UfYlZ',
	    'PoUF^F R`FlF RURiR RVV^V Ra[l[ RUJUR RVSV[ RaSa[ RUJiJiR RWFXH RcFfH RlXk[ RUCTH RaD]H RZMdP RdL_NXP RjUaW RYZR[ R^ZWZ',
	    'PoUF_F R`FlF RWQjQ RWSiS RWWiW RWZkZ RSLSN RXPX[ RaOaZ RSLkLkO RWFZH R`MbP RdFfH RbD]H RVCTH R_IZORR',
	    'PoUG^G R`GkG R^OhO R\\SkS R_[c[ RTJT[ RcOc[ RXFYI RgVkZ RcFfI R^VZZ RaE]I RXQZU RdIfLlO RUCSI RTKZKWP RbI_MZO RYVVW',
	    'PoUF^F R`FlF R^JlJ RTQ\\Q RTZ\\Z RTLT[ RTL\\L\\P RTT\\T\\[ R_N_X ReJe[ RcFfH RUCTH RWFXIVK RaD]H R_NkNkXgX',
	    'PoUF^F R_FlF RUK]K RaLiL R]QfQ RaTkT Rd[k[ RaJaS ReUe[ RkXk[ RWFYH RUCTH RaC]H RYNjNjQ R_U]YY[ RUJUSTY RYNYSXY R_JZJ RiH^J',
	    'PoUF^F R`FlF RTJ]J RhPlP RVRfR RUUjU RSXlX R[[_[ RXJXO R`R`[ R`JhJhP RlMlP RXFYH RbLeO RdFfH RUCTH RbD]H R_M\\Q R_J_O R\\NRP',
	    'PoaFkF RWPhP RWShS RXZhZ RWMWVX[ RUNiMhS RXVhVi[ RYFYH RVERH RbC`G R_K_M RUF^G\\JSN R_IfMlN RVPUY',
	    'PoTG^G R`GlG RTJjJ RRNlN RUTiT RUXiX R_[k[ RUQUY R`Q`[ RUQiQiX RWFXI RcFfH ReJdM RkXk[ RaE^H RZJZM RUCSH',
	    'PoUG^G RaGlG RaMkM RR[l[ RUKUR RVUV[ R[J[S R\\U\\[ RbUb[ RVUiUi[ RYFZI ReOgS RdFgI RVERI RbC`H RbJ_Q',
	    'PoUF^F RaFkF RTL\\L RTP\\P RRT\\T RWPW[ RlYk[ RZFZI RdFgH RVDRH RbD^H Rh[k[ RVISO R\\XV[ RkShV_Z RcIcNeX ReJhL`M RjLdM RlP]S',
	    'PoUF^F R`FlF RSK`K RRQaQ RUZ_Z RUTU[ RVMVP RZIYK RUT_T_[ RcKc[ RcFfI RaE]I RVCTH R]M\\P RcKkKiNhQlUkXfX',
	    'PoUF^F R`FlF RSJlJ RWQhQ R]XeX RZXcX Re[j[ RUTU[ RWKWQ RhKhQ RUTjTj[ RcUfY RXFZH R^H`J RdFfH R`Q^TZX RUERH RbC^H R[LeO RdK_NYO',
	    'PoUF^F R`FlF RSJ^J R`JlJ RTM]M RaMkM R`PlP RUQ]Q RSQ[Q RRWlW RUZhZ RXIXR ReIeR RUShSh[ RVERI RbC]H',
	    'PoUF^F R`FlF RUMiM RUPiP RRSlS RSVkV RSXkX RUJUP RXPXU R_J_[ RfPfU RUJiJiP RWFXH RcFfH RUCTH RaD]H',
	    'PoUG^G R`GlG RZKlK R\\[l[ ReReW RWFZH RUJXM RgSkV RcFfI RRSVSVWR[ RaE]I RcOdShS RSNWO RVCSH RdK[O RbO[S RhOdP RaMeQaTZW RdY`Y RYZa[',
	    'PoUF^F R`FlF RcUjU Rb[l[ RWPW[ RcQcZ R]VaZ RXFZH R_LaO RgLkP RcFeH RVERI RXISN RaD]H RYMRT R^LZQ R_J]O R^SZZ RgJeO',
	    'PoUF^F R`FlF RSJkJ R_LiL RRPlP RXSjS Rf[j[ RZXZ[ R_J_O ReWgY RWFXH RcFfH RUERH RaD]H R_W`Z RTWS[ RYLYQWVkVj[',
	    'PoUF^F R_FkF R\\RjR R]UiU R[WkW R\\[l[ RcPcY RWFXH RcFeH RaD]H RaH\\M RTJXK RSNWO RUCTH RRSWSWXSZ R_LdNkP R_KhKfMbOZQ RYZa[',
	    'PoUF^F R`FkF RTKhK RSP[P RbPkP RWFXH RiNkQ RcEfH RUCTH R\\HYK RaD]G R`NjS RcIiL RWLUP ReLdP R_N]ORT R`RVT RcTWX RdXV[',
	    'PoUF^F R`FkF RVKhK RVMhM RVPhP RRRlR RXXfX RY[h[ RVIVP RYTY[ RWUfTfX RVIhIhP RcEeH RVDRH RbC^H ReSlW R\\PXTRW RYGYI',
	    'PoUG^G R`GlG RRN[N R]OjO R]SjS R[WlW RS[W[ RWJW[ R]K]S RdKdS RfTf[ R]KjKjS RcFfI RaE]I RXFXH RVCSI RbT]V R[RRS',
	    'PoUF^F RbFlF RSJkJ RTPjP RVRhR RSVkV RTMTP R[J[P R_V_[ RbJbP RTMjMjP RXGZH ReGgI RVDRH RdXk[ RbCaG R[XS[',
	    'PoUG^G R`GlG R[OcO R[ScS RUSYS ReSkS RUKUT R[K[S R[KcKcS RfKkKkT RcUc[kZl[ RcFfI RaE^H RVCTH R[VYYT[ RYJUK',
	    'PoUF^F RbFlF R[LbL RYPcP R_UcU RhXjX RVPV[ R[S[Z R_L_Z ReKe[ ReKkKkW RYG[I RfGiJ RVERI RWJRN RWORT R\\IZN RcCaH RbYZZ',
	    'PoUF^F R`FlF RUNjN RURlR RUJUN R\\J\\N RcJcN RUJjJjN RXFYI RcFeH RVERI RbD]H RdOgP R_PbVeYi[ RUT\\W RhSdW[[ RlXk[h[ RURTY',
	    'PoUF^F RbFlF RSJkJ RUNiN RUQiQ RRWlW R`[d[ RULUQ R_H_T ReUe[ RULiLiQ RVDRH RXG[H RTTfSjU RXX\\Z ReGhH RcCaG',
	    'PoUF_F R`FlF RUIjI RVLjL RSNlN RVSiS RVUiU R_H_N RVPiPiX RcEfH RVDRH RbC^H R\\YS[ RVQVXiX RfYm[',
	    'PoUF^F R`FlF RcLjL RZPlP RcUjU Rb[l[ RWPW[ R]J]P RdIdZ R]VaZ RXFZH RcFfH RVERI RXITM RaD]H RYMRT R]SZZ',
	    'PoUF^F R`FlF RVNhN RXT^T RTZkZ RTJT[ RXPXT RXP^P^T RTJkJk[ RbTfX RXFYI RcFfH RVERI RbC]H RcLfL R`LcU RfQbU^WVW RiVhXfX',
	    'PoUF^F R`FlF RRO\\O RXKX[ RWFYI RWP\\T R`LdN RcFfI RaE]I RVCSI RXNTW RcK]M RbKjKfO^R RdR\\V RcSkSgW_[ RbUfX][ R[JSK',
	    'PoUF^F R_FlF R^JdJ RUOiO RZSgS RXXiX RY[k[ RYUYZ RZPZS R`U`X RZPgPgS RXUiUiX RUJZKiLiO RWFYH RUERH RaC]H RUJUSTY RhH`I',
	    'PoUF^F RaFlF RUJkJ RUNkN RYSjS RZViV RV[l[ RaPa[ RZL]N RdFgH RdLbN RWDSH RbC`G RZQWU RUNUTTZ',
	    'PoUF^F R`FlF RUN]N RaPjP RSR^R R[[k[ R[R[Z R_H_K RTJTLkKkM RdFgH RkXl[ RVCTH RaD]H RaRfVjX RbM_S RTZR[ RhPfTcV_X RWRUY',
	    'PoUF^F R`FlF R_MlM RRM]M RaQkQ R^TlT RWFYI RVQ\\Q\\Z ReSfWj[ RcFfH RaE]I RVCSI R`I^P RbN_S RdVaY^[ RW[[[W[ RWJWMUX RePdW',
	    'PoUF_F R`FlF RUNjN RbRkR RWR`R RWV`V RbVkV RfRf[ RUJjJjN RcFfH R\\Q\\XX[ RaD]H RXFXH RVCTH RUJUSTY',
	    'PoUF^F R`FkF R]LjL RZOlO R]WiW R\\[l[ R]S]X RcIcQ R]SiSiX RXFXI RcFfH RVCTH RaD^H RUJYK RTMWO R^I\\N RRSWSWXRZ RYZa[',
	    'PobFlF RUF^F RTKjK RYNeN RYQeQ RRTlT RXWfW RT[j[ R_U_[ RYGYH RfGhH RUCTH RcD_H RfJeThWlY RYJYTVWRY',
	    'PoUF^F R`FlF RWMhM RWPhP RUT]T RST[T RR[l[ RUVU[ RWJWS R\\V\\[ RbVb[ RWJhJhP RUViVi[ RWFXI RcFfH RUCTH RaD]H RiQbRkU',
	    'PoUF^F R`FlF RSK^K RTS^S RTOTS RYIY[ RTO^O^S RfVk[ RYU\\V RcFfH RdV_Z RaE]I RYSRY ReRhY ReRbX RaMkMjR RVCSI RbJ_R ReMdV',
	    'PoTF^F R`FlF RRKlK RVNhN RUSiS RRUlU RS[Z[ RUPUS RVIVN R_F_S RVIhIiN RUPiPiS RWEXG RVDRG RbC^G R\\SWXj[ RfVaYU[',
	    'PoUG^G R`GlG R]LkL RSM[M R\\PlP R^VjV R^ZjZ RT[W[ RWJW[ R^S^[ RaJaP RgJgP R^SjSj[ RbFeI RXFYJ RVCTI RaC_H R[RRT',
	    'PoUF^F R`FlF RUKkK RTSkS RYV`V RYZ`Z RV[][ Rc[k[ RYRYZ R[K[R RcKc[ RUNkNkR RXFYI RcFfH RkXk[ RVERI RbC]H RjUbW RUKTSSZ',
	    'Po`FlF RTF^F RSL_L RSR^R RSU^U RRX_X RSNSU RYJY[ RSN^N^U RgVl[ RXFYI RdFfH RdV_Z ReRhY ReRcX RUCSH RaC_H RbMkMjR RbJ`R ReNfV',
	    'PoUF^F R`FkF RSKkK RRPlP RWSgS RWMWS RZVZZ R_I_K RWMgMgS RWFXH RcEfH RVCTH RaD]H R_ScXj[ RjTcW R_SZVRX RaYV[',
	    'PoUF^F R`FkF RVKhK RVNhN RVPhP RRSlS RX[g[ RVHVP RVHhHhP ReXg[ RcXfY RdEeH R`V[Z RVDRH RbD]H RdTlX R\\PXURW R[VVX',
	    'PoUF^F R`FkF RWM^M RWP^P RWX]X R\\[`[ RWSWY RZKZS RWS]S]X RTJ`J`[ RXFYH RcFfH RVCTH RaD]H RiIgKbN RjNhQbS RlThXa[ RTJTTSZ',
	    'PoUF^F R`FlF RSKkK RUMiM RRPlP RTSjS RYMYS R_I_K RfMfS RWFXH RcFfH RVCTH RaD]H RiUcX RYVYZ R_ScXgZl[ R_SZVRX RaYV[',
	    'PoUF^F RaFlF RRK`K RVN]N RST_T RRW_W Rf[j[ RSQST RVIVN RYNY[ R]I]N RSQ_Q_T RYFZH ReFgH RcW_[ RVDRI RbC`H R`NlNkUj[ ReKcW',
	    'PoUF_F R`FlF RVP`P RbPlP RTZ_Z RTKTY R[J[X RiPi[ RZP_T RXFYH RdFfH RVDRH RbD^H RYRVU RWKXN R[NXT R^K^N RbLbUa[ RkJbK',
	    'PoUF^F R`FkF RbRfR RSUlU R_R_[ RXFYH RiLkO RcEfH RVDRH RaD]H RYNTR R]LZO RUIXJ RSLWM R_UjZ RbJ`O^QZR R_UTZ R\\JgJfR',
	    'PoaFlF RUF^F RRLlL RTOiO RWTgT R_H_[ RUJiJiO RiQi[ RYQ[R RdFgH ReQbT RWDRH RbC`G RYFYH R]TgX R_S[WVY RURTY',
	    'PoUF^F R`FlF RZJlJ RRNZN R\\OjO R\\RjR RYVlV Rb[f[ RVIV[ R\\M\\T RcHcT RgUg[ R\\MjMjT R]X`Y RXFYI RcFeH RVERH RbD]H RgHjI',
	    'PoTF^F R`FlF RTNjN RRQlQ RTTTY RTJTN RYQY[ RTJjJjN RcFfH RkXl[ RUCSH RaD]H ReOhP RaQdWfZj[ RiTeX_[ R[J[N RaNcV RbJbN RTT^T^Y[Y',
	    'PoUG^G R`GlG RRL`L RVO]O RbPlP RVS]S RRV`V RVIVV R]I]V RhPh[ R[X^Y RcFfH RVXS[ RaD]H RWFXI RUCSI RjIbKbTaY',
	    'PoTF^F R`FlF R\\MiM R\\OiO RYRlR Re[i[ R\\J\\O R\\JiJiO RYFZI RdFfH R^RYW RaU[Z ReVa[ RUERH RTJXK RROWP RVUTZ RaC_H R\\UjUi[',
	    'PoUF^F RbFlF RSKkK RXRfR RXVfV R[[_[ RXOXV R_V_[ RWPfOfV ReXhY RfGhI RXWUZ RVCTH RcD_H R]JXO RiLfP RYGYI RaKeOlR RULYORR',
	    'PoUF^F R`FlF RUM]M RaMjM RVRiR RVUiU RRXlX RUJUM RVOVU RUJ]J]M R_O_[ RaJaM RVOiOiU RaJjJjM RWFXH RcFfH RUERH RaD^G',
	    'PoUF^F R`FlF RTIkI RUOjO RVSiS RVViV RRXlX RULUO RVQVV R[I[O R_V_[ RcIcO RVQiQiV RULjLjO RVDRH RaD^H RXFYH',
	    'PoUF^F R`FlF RSJkJ RUOiO RURiR RR[l[ RUMUT RUVU[ R\\V\\[ R_G_T RbVb[ RUViVi[ RWFXI RdFgH RUCTH RaD]H RaHfJ RUMiMiTgT',
	    'PoUF^F R`FlF RTM]M RaMkM RTP]P RaPkP RZVdV RZYdY Rf[k[ RTJT[ RZSZZ RTJ]J]P RaJaP RZSdSdY RaJkJk[ RWFXH RcFfH RUERH RbD]H',
	    'PoUF^F RaFlF RTM]M RaMkM RTP]P RaPkP RXRgR Rf[k[ RTJT[ RXFYH RTJ]J]O RaJaO RaJkJk[ R_PaR RdFgH RVDRI RbD^H R\\UeY RdR`VYY RZTcW',
	    'PoTF^F R`FlF RVLhL RRNlN RVShS RVUhU RVXhX RVPVX RVIVL R_H_N RVIhIhL RVPhPhX RWEXG RVDRG RaC]G RcZl[ R[YR[',
	    'PoTF^F R`FlF RTMjM RVRiR RVUiU RVXiX RVOVX RVOiOiX RTJjJjM RcFfH RUERH RaD]H RXFXH R[J[N RcJcN RTJTN RcYl[ R[ZR[',
	    'PoUF^F R`FlF RSMkM RYRdR RZYdY Rf[i[ RUTU[ RYNYR RZVZY RZVdVdY RXOeOeR RUTiTi[ RWFXH RVCTH RaD]H RbMkQ RUK^JZMRQ RbJ]J RfI`J',
	    'PoaFlF RUG^G R[PdP RWWfW RR[l[ RWSWW RWSfSgW RVMXP RYFZI RdFgH RgKcM RcXb[ RVERI R[Y[[ RaJeOlR RbC`H RTK]KZORR RjMeO',
	    'PoUF^F R`FlF RSJkJ RZMdM RROlO RUUiU RUXiX RURUX RZIZM R_O_X RdIdM RURiRiX RWFXH RcFfH RUCTH RaD]H R[YS[ RcYk[',
	    'PoUF_F R`FlF RUL]L RaLjL RUO^O RaOkO RTRkR RSVlV RUJUO RZOZV RUJ]J]M RbJbO ReOeV RaJjJjM RcEfH RVDRH RaD^H RcXl[ R[XS[',
	    'PoUF_F RaFlF Rb[g[ RSQST RbC`H RSQkQkT ReMiO ReFgH RbJaM RjJhL RVERI RUJTM R]J[L RXM\\O RWNSP RdN`P RVUhUg[ RZFYIWN ReIdN R_S]W[YS[',
	    'PoUF^F R`FlF RTJ^J RaJkJ RRM^M R`MlM RVWhW RVZhZ RVSV[ RVShSh[ RWFXH RcFfH RUCTH RaD]H RgQlPlQ RYMUQRR RdP`R RZMZQ RgMgQ RTJTM RYJXP RaJaM RfJeO R_PYQ',
	    'PoUF^F R_FlF RRMlM RUOiO RUVfWjX RUS[R[S RTY[X[[ R_H_[ RcXc[ RUJiJiO RcQcSjRi[ RWFXH RcFfH RUCTH RaC]H RURTZ',
	    'PoUF^F R_FkF RbKiK RTP\\P RXS\\S R`ZjZ RTKTP RTRTZ RXPXY RTK\\K\\P R`U`[ R`UjUj[ RWFYH RcFeH RbK]P RUCTH RaD]H RaLfQlT RiLdR^T R]XRZ',
	    'PoTF^F R`FkF RTJjJ RRQlQ RWZgZ RWTW[ R_J_P RVUgTh[ RXL[O RXF[H RfMiO RdFgH RfKbO RUERH RYKUO ReRkU RaC_H RZQXSRU',
	    'PoaFlF RTF^F RTK^K R`KkK RRN^N R`NkN RWWhW RWZhZ RWSW[ RWShSh[ RdFgH RUCTH RbD^H ReMhPlR RXO]Q RXFYHYNVQRR RdP`Q ReJeO',
	    'PoUG^G R`GlG R\\LkL RRM[M R_PhP R_ShS R[WlW RWIW[ R_I_V RhIhV RgXjZ RWO[S RWFYI RcFfI RaE]I RVCSI RWMSV RaX[[',
	    'PoUF^F R`FlF R]OjO RTO[O R]SjS R\\WlW RYJYO RTSZSZ[ R]K]S RfTf[ R]KjKjS RdFfH RdKcS_W RaE]I RUCSH RTJTSS[',
	    'PoUF^F R`FlF R^LlL RRM]M RaOiO RaSiS R^VlV RW[Z[ RaJaV RiJiV RWFXH RcFfH RUCTH RaD]H RhXlZ RbX\\[ RVNTY RVQ[P[VZ[',
	    'PoUF^F R`FlF RTOZO R]OjO R]SjS RTTZT R\\WlW RW[Z[ RTJZJ[[ R]K]S RfTf[ R]KjKjS RdFfH RdKcS_W RaE]I RUCTH RTJTVSZ',
	    'PoTF^F R`FlF RRK^K R`OjO RTQ]Q R_TlT RTT]T RRW^W RTNTT RXIXN RXTX[ RTN]N]T ReOe[ ReIjM RcEfH ReI_N RUERH RaD^G',
	    'PoUF^F R`FlF R_JiJ R^OgO R\\OdO RXOaO R_QkQ RVWlW R]S]W R_I_L RcScW RYSZV RWFXH RcFfH RhSfV RVCTH RaD]H R_M_Q RULkLkO RcYk[ R\\YU[ RULUSSZ',
	    'PoUF^F RbFlF RRL^L RSR^R RSU^U RRX_X RaZjZ RSOSU RXJX[ RSO^O^U RaUa[ RaUjUj[ RYG[I RfGiI RcCaH RbMeQlS RVCTI RcJ`P RbMkMgP`S',
	    'PoUF^F R`FlF RSJ[J R_NiN RRO\\O R]QlQ Ra[d[ RSRSX RWOW[ R[R[X RdQd[ ReTjY R^S`U RWFXH RcFfH RjSgV RUERH RaD]H RdQfV RbW\\Y R`I_O R`JiJhQ',
	    'PoUF^F R`FlF RULlL RcNkN RZOhO RWQlQ RYSiS RYZiZ RYXY[ RcKcN RYXiXi[ RdEgH RVDSG RbC^G RYIcI`K RXEYGWKRM R\\MWN RfUiU R\\UYU RcUfU RZU]U`UcU RVLUSTY R_U\\U RhUeUbU_U',
	    'PoTF^F R`FkF RRL^L RUO\\O RUR\\R RRV_V RUJUV R\\J\\V RfJfQ R`LlLjO R[X]Y RVXR[ RUCTH RaE]H RXFYI RbQdVhYl[ R`QjQhVeYa[ R`L`T_Z',
	    'PoUF^F R`FlF RXQfQ RUWiW RUZiZ RTKSN RUTU[ RXNXQ R_I_L R_T_Z RXNfNfQ RUTiTi[ RTKkKkN RXIZK RgIdK RUERH RbD^H',
	    'PoUF^F R_FlF RUMhM RVPhP RRSlS RXWeW RT[j[ R_H_K R_U_[ RSJSLkKkM RXFXI RcFfH RVCTH RaD]H RdLdTfVlX RZLZTXVRX',
	    'PoUF^F R`FlF R_VfV RgYjY R[[l[ R[P[X R_S_W R^MdMdP R_SfSfV R^JhJhP R^J\\PkPkX RUJWM RXFYI RdFfH RUXR[ RaE^H RUCTH RSQWQWX[Za[',
	    'PoTF^F R`FlF R[KkK RRMZM R\\NkN R[PlP R]UjU RZXlX RS[V[ RVJVZ R]R][ R`J`P RcPcW RgJgP RdFfH RaD^H RUCTH RZRRT R]RjRj[f[',
	    'PoUF]F R_FlF R[NcN RVU]U RaUhU RVPVU RWFYI RVP]P]U RaPaU RaPhPhU RdFeJ R_HbKlP RXX\\Y RVCSI RaD]H[LRP RYVVYR[ RcV`Z][ RbXkZ',
	    'PoUF^F R`FkF RUJkJ RWNjN RVSlS RXVhV R^K^[ RcLc[ RXPhPhV RWFXH RcFfH RVCTH RaD]H RcUfXk[ R]UZYU[ RgKaK RUJUTSZ R_KXL',
	    'PoUF^F R`FlF RZKlK R[PkP R[TkT RYVlV Rb[f[ R[M[S RcIcS RfTf[ R[MkMkS R]X`Z RWFZH RcFfH RVERI RbC]H RTJXL RhIkI RRPWQ RSZXT',
	    'PoUF^F R`FlF RRMZM R\\WjW R\\ZjZ RS[V[ RVIV[ R\\J\\P R\\S\\[ RcScZ R\\SjSj[ RWFXH RcFfH RUERH RaD]H RfKdQ RjQgQ RbI\\J R`MaPZP RZSRT RcJkJjQ',
	    'PoUF^F R`FlF RSK^K RTS]S RTNTS RXIX[ RTN]N]S RaOaW RaOjOjW RhWl[ RXR]W RYFYH RcFfH RcKhKfO RVCTH RaE]H RWURY RbL_O RXQVV RcIaN RdY_[ RfQeX',
	    'PoUF^F R`FlF R`LlL RVM[M RTQ\\Q RTT\\T RTV\\V RTY]Y RTOT[ RTO\\O\\T R`O`X ReKe[ RYI\\M RcFfH RVCTH RXIRN RaD]H RaI^O R`OkOkXhX',
	    'PoUF^F R`FlF RTJ^J RbMlM RTP^P RbRkR RTS^S RR[l[ RTJTS RUVU[ RYJYM RYPYS R\\V\\[ RTM^M^P RbVb[ RUViVi[ RWFXH RcFfH RUCTH RaD]H RcJ`O',
	    'PoTF^F R`FlF RTIkI RYNgN RWOjO RYShS RYVhV RT[l[ RTIT[ RYKYN R`J`O RYKgKgN RYQhQhX RVDRH RbC^G RYQYXeXjZ R]YWZ',
	    'PoUF^F R`FkF R]KbK RUNZN ReNiN RURZR ReRiR RRVlV RVKVV R]Q]V R]I]NbObV RdKjKjV RcFfH RVCTH RaD^H RXFYH RcXk[ R[XS[ RZJUK',
	    'PoUF^F R`FlF RSIkI RWOiO RWRiR RWTkT RTWhW RS[Y[ RXMXT R\\H\\K R`M`T RcHcK RWFXH RcFfH RVDRH RXKTP RaD^G R[Yj[ ReYW[',
	    'PoUF^F R`FlF RRJlJ RWNhN RVRhR RWLWN RWLhLhN RSPSQkPlR RWDRH RbD_G RcVgYk[ R^UUX RhT^W R_TaW\\XT[ RaWa[][ RbRUT',
	    'PoTF^F R`FkF RRJ^J R`JkJ RSR^R RSV^V Ri[l[ RSMSR RWJWR RZJZR RSM^M^R RbTa[ RbObW RbOiOi[ RlXl[ RXFYI RcFfH RUCTH RaC_H RcJcSfV RgZ`[ RXSZZR[ R^YUZ',
	    'PoTF^F R`FlF RSIkI RULjL RVPiP RWWhW RUYiY RR[l[ R_H_K R`V`[ RVNVOhNiP RSRSSkRkT RVTgThV RXEZH RdEgH RVDRH RbC^G R_TWV',
	    'PoUF^F R`FlF RVRjR RWUiU RWXiX RW[l[ RWQW[ RTI^I^O R`R`Z RaIjIjO RVERH RbD^H RYOSU R[LTO RhIaL RhLaO R[ITL',
	    'PoTF^F R`FlF RRIlI RUMU[ R_I_[ RSNjMj[ RcEfH RXV[VWZ RfOaT RbVfVaZ RUDRH RaD]H R\\OWT ReKlN RbPfR RXP\\R RYKRN Rf[i[f[',
	    'PoTF^F R`FlF RSIkI RUKiK RWPgP RURiR RRTlT R^WlW RTY\\Y Rd[h[ RTVTZ RTV\\V\\Z R_P_R RhTh[ RRNkNkP R`XbZ RXEXH RVDRG RaC^G_K',
	    'PoaFlF RVF^F RSK\\K R_KlK RTN\\N R^PlP RSR]R R`VkV R`ZkZ RXIX[ R`S`[ RcIcP RiIiP R`SkSk[ RWR]W RYFZH RWESI RbC`H RXRRY',
	    'PoUF^F RaFlF R_JkJ RTN]N RTR]R ReTkT RRU^U Re[l[ RUIUN RfPfZ R`VdZ RYFYH RdFgH RVERI RaLgN RXW\\Y R_PkPjR RUOST RbC`H RjJdM RXWVZR[ RaS_Z R\\JTK RXQXX',
	    'PoUF^F R`FlF R_KlK RRL]L R^NlN RaQiQ RaTiT R^WlW RW[[[ RaLaW RiLiW RcFfH RVCTH RaD]H RhYlZ R`H^L RbY][ RWFXIVPUX RVP\\P\\W[[',
	    'PoUF^F R`FlF R^MhM RTOYO R[QlQ RTUYU R`[c[ RW[Y[ RTJYJY[ RcSc[ R^T`V RWFXH RfOkT RcFeH RhJkM RaO[T RhTfV RUCTH RaD]H R_J\\M RcWjZ RcI_R RaX\\Z RTJTUS[',
	    'PoUF^F R`FkF RRPlP R[RcR RXWfW RX[_[ RV[\\[ RXSX[ RdEeH RVDRG RaD]H RXSfSlU R\\NXRRU RYN\\N RdPgSgXbY RbNeN RVNZN R^WbZj[ RYJ\\JYJVIVMfKhJeJbJ RXEWHhHhNdN R]J`JbJ_J\\J R`N\\N R_NbN`N RXU\\U^UbUfUcU`U]U',
	    'PoUF^F R`FlF R[JlJ R\\LaL RRMZM RePjP R\\PaP R\\WjW R\\ZjZ RS[V[ RVIV[ R\\L\\P R\\S\\[ RaJaS RcScZ ReJeS R\\SjSj[ ReLjLjP RWFXH RcFfH RUERH RaD]H RZSRT',
	    'PoUF^F R`FlF RYNlN R[WiW R[ZiZ R\\R\\[ RZJ`JcJbR RbSbZ RZSjSi[ R]K^N RWFZH RbFeH RgJfN RVERH RaD]H RcNfQlS RROWO RVTS[ RaN^QXS RTJXK RhI`J',
	    'PoTF^F R`FlF RRLaL RTN_N RTS_S RRUaU RTJTN RTJ_J_N RTQTR_Q_S RfUk[ RhTbZ RUCTH RaD]H RcOgX RjNfW RcMeU RYHYSUX^Z RcLkLgU RcEeHaQ R^U[YS[',
	    'PoUF_F R`FlF RSM[M R^WjW R^ZjZ RXIX[ R]J]P R^S^[ RdSdZ ReJeR R^SjSj[ RWO[S RXFYH RcFfH RbP_R RaD]H RVCTH RWMSW RbI]J RbO\\P ReJkJkPhP RbLbQ',
	    'PoaFlF RUF^F RTJaJ RRNlN RSQXQ R]QbQ RTTXT R]TaT RSWXW R]WbW RRZ_Z RXNXY R[I[N R]N]Y RhIjL RVERI RbD^H RePgY RjPgVbZ_Y RlYk[iZ RdJfW',
	    'PoUF^F R`FlF RUIjI RVM\\M RbMiM RVO\\O RbOiO RVSjS RWUiU RWXiX RWZkZ RSKSN RWRW[ R_I_P R`Q`Z RSKkKkN RVDRH RbD^G RXQSV ReGeI',
	    'PoUF^F R`FkF R[OlO RTOYO RTUYU R\\WjW RW[Y[ RTJYJY[ RcVc[ RgXj[ R_ScT RWFXH RgTjW ReNkS RcFfH R^LhKjM RaN[T RbQ`S R_X][ ReRaV RUCTH RaD]H R_J\\M RcI_Q RTJTUS[',
	    'PoUF^F R`FlF R`NiN RTO[O R]QlQ RRR\\R Ra[e[ RTTUW RXOXY ReQe[ RfUkZ RXI\\M R_SaU RXFYH RcFfH RjSgV RXIRO RaD]H ReQhW RVCTH R[T[W RcW]Y RaH`O RaKiJhQ R\\XRZ',
	    'Po`FlF R_IhI RXN_NcNiO RYSgS RYUgU RU[l[ RXXX[ RYQYU R^X^[ R^L^O RUF^F^K RcXc[ RYQgQgU RYWiXi[ RWFXH RUCTH RaD]H R`Q`U RUKkKkN ReGeI RUKUSSZ',
	    'PoUF^F R`FlF RbKkK RTO\\O RSR\\R RbTjT R^TfT RTTUX RXOXZ RlYl[ RXI\\M RXFYH RbIdN RdFfH RVCTH RXIRO RaD]H ReQgR RaRcWfZj[ RdIfI RkNkQ R[T[W R^LdKeOkQ RhMcO^Q RiVbZ][ R\\YRZ',
	    'PoUF^F R`FlF R\\MkM RRMZM R]OjO R[RlR R[UlU R\\XkX RS[V[ RVIV[ R\\I\\L RaIaL RcMcR RcUc[ RfIfL R\\IkIkM RWFYH RcFeH RUCTH RaD]H R_R`U RgRgU RYRRS',
	    'PoUF^F R`FlF RTHkH RUJjJ RUOhO R[RaR RTTYT RbUgU RTWYW Ri[l[ RV[Y[ RUHUJ RULUO RTQYQY[ R_E_H RULhLhO RcQiQj[ RlXl[ R\\W_X\\[ RVDRG RaC^F RaT^Z R^P\\X RUQUTSZ ReRdY',
	    'PoUF^F R`FlF R[JcJ RYLcL R^NcN RhPkP RYQ`Q RVSiS RRVlV R[[_[ RVLVR R^I^Q R_V_[ RfIfR RfIkIkP RVDRG RWJSN R[HZJ RbC^G RVHSJ ReWlZ RYXR[ RZMZQ RcP^Q',
	    'PoUF^F R`FlF RSK^K R`RjR RTS]S R`UjU RTNTS RXIX[ RTN]N]S R`PjPjX RWFXH RcFfH RaD]H RWURY R`P`XiXl[ RXU\\W RVCTH RXQVV ReJbM_N R`JkJjNgN RcY^[',
	    'PoUF^F R`FkF R`JkJ RSK^K RRO^O R`PkP R`SjS RTU]U R`UjU RTX]X R`XjX Ra[l[ RY[][ RTRT[ RXHXK RTR]R][ RaPa[ R`H`MjMjP RcEfH RUCTH RaD]H RULVN R[L[N',
	    'PoTF^F R`FkF RRMZM R_NgN R\\TaT ReTjT RWIW[ R\\Q\\T R\\QaQaT ReQeT ReQjQjT RXFXI RfXjZ R_WaZ RcFfH R_W[[ RgWc[ RaE]H RdIfLlN RUCSH RbI_MZO RZRRT RS[V[S[',
	    'PoUF^F R`FlF RbNlN RVN]N RTTXT RZT_T RTPTT RTPXPXT RZPZT RZP_P_T ReUk[ R\\W_Z RUWXZ RcFfI RgUbZ RZITN RaE]I RbNfW RYFYI RYJ^L RiOfW RUCSH RUVS[ R\\VZ[ RcIaR',
	    'PoUF^F R`FlF RTK]K RbKkK RTN]N RbNkN RXPfP RYWeW Rg[k[ RTIT[ RYSYV RTI]I]N R_O_[ RbIbN RYSeSeV RbIkIk[ RUERH RbC^H RaXfZ R^WXZ',
	    'PoaFlF RUF^F RRNlN RSQXQ R]QbQ RTTXT R]TaT RSWXW R]WbW RRZ_Z RXNXY RYFYH R]N]Y RWJYL R^JaM RhJjL RVERI RWISM RbD_G R_J[M RjPgVbZ_Y RlYk[iZ RdJfW',
	    'PoUF^F R`FkF RTRZR R\\RbR RdRjR RUWiW Re[i[ RTNTR RUTU[ RTNZNZR R\\T\\[ R\\NbNbR RbTb[ RUTiTi[ RdNjNjR RWFYH RcFfH RVDRH RbC_GiL R[KRM R_HTM R\\N\\R RdNdR RZLcKlM',
	    'PoVF^F RaFlF R^MlM RTO\\O R_RjR RSS\\S R_UjU R_XkX R][l[ RUUUX RXOXZ R_O_U R^JfJe[ R_OjOkU RYJ\\N RZFZH RWESI RXIRO RbC`H R[U[X R\\YR[ RjIdJ',
	    'PoUF^F R`FlF RaIiI R^NcOhO R]QkQ R[[l[ RaHaK RbLbO RUHXK RXEYH RRRVRVWR[ RVDRG RaC]G RbQdTjW RSMWO R[KkKjN RcS]V RiRdSeV]X ReQ\\S RdYaY RVXZZa[ R[K[RYW',
	    'PoUF^F R`FlF RTN]N RSTXT RZT_T RSPST RSPXPXT RZPZT RZP_P_T RhWl[ R\\W_Z RUWXY RcFfI ReV`[ RYFYITN RaE]I RfRiY RYJ^L RfRcY RcLkLjP RUCSH RUVS[ R\\VZZ RdIaP RfOgSeW',
	    'PoUF^F R`FkF R\\MiM RZQkQ RdSiS RZUkU RcZhZ R[[l[ RUHXK RXEXH RRRVRVXR[ R_WhWgY RVDRG RaC]G RRMVN R_R[S R_W[Y R_ObO R\\I\\O_O RfKiKeK ReObO R_K\\K_K RaK_K R\\IiIiOeO RaKcK RXZa[ RdKbK',
	    'PoUF_F R`FlF R[O`O RcPlP RTQ`Q R[X`X RTY_Y RTJTY RhPh[ RXFYH RdFfH RVDRH RbD^H RYLUM R_L[M RYOUO RYTUU R_T[U RYWUW RcKcTaZ RkJbK',
	    'PoTF_F RaFlF RbJkJ RUJ]J RSM^M R`MlM RhPlP RVShS RVVhV RYHZMZP RfIgLhO RVQhQhX ReMbP RVDRH RUITL RWNTP RbD^H RbIaL RVQVXfYl[ R^OYO R[ZR[',
	    'PoUF^F R`FlF RRK`K RaMlM RbQkQ RTQ^Q RbUkU RTX\\X RbZlZ R[[^[ RSTS[ RTLTQ R^L^Q RST^T_[ RbKb[ RgKgZ RZU]X RcFfH RUERH RaD]H RWFYJ RhIgM RYQVX RcIbQ R[MWP RXNZO',
	    'PoUF^F R_FlF RTI^I RaIkI RTOkO RYW_W RY[_[ RV[][ RUKUN RYTY[ R[O[T RUK\\K\\N RbKbN RTRiRiU RbKjKiN RUERH RaC]G RcOc[kZk[ RTOTVjVbW RTRSZ',
	    'PoUF^F R`FlF RTMjM R_QkQ R_SkS RRU\\U R_WkW R_ZlZ RTITM RXUX[ R`O`[ RfOfZ RTIjIjM RZS]U RSPWS RcFfI R`P]S RVDRH RXMTP RaD]H RZOUU R[W\\Z RWFYI RTWS[ R[I[M RcIcM RaM`P RfMeT',
	    'PoUF^F R`FlF RTHkH RUJjJ RUOhO R\\SaS RTTYT RcUhU R\\UaU RTWYW R\\XaX Ri[l[ RV[Y[ RTHUJ RULUO RTQYQY[ R\\Q\\X R_E_H R\\QaQaX RULhLhO RcQiQj[ RlXl[ R]Y[[ RVDRG RaC_F RUQUTSZ ReSdZ',
	    'PoTF^F R`FlF RTK]K RWPjP RWUkU RTWhW RS[Y[ Rf[l[ RTH]H]M RaHjHjM RWEXH RcEfH RVDRH RaC^G RYMTR RXOWS[SWS RcS_S RdMgM ReSiS RjIeJaIaMcLjLgMdM RaIeJ RTHUMYM RgSdSgS R_O`SdS RYX`Zi[ RXJTJ R]IYJ R\\MXM R\\S_S[S RdYV[',
	    'PoaFlF RUF^F RUIjI RVM\\M RbMhM RUP\\P RbPiP RaSkS RTS^S RaUkU RTU]U RaXkX RTX]X RTZ^Z Ra[l[ RSKSN RURU[ RZRZZ R_I_P RbRb[ RgQgZ RSKkKkN RbR_U RWCSH RURRU RbC`G',
	    'PoUF^F R`FlF RaIlI RWL^L RbPkP R]Q`Q RbSkS RbVkV RTW_W R][_[ RTTT[ RXTX[ R\\T\\[ R]N^N]R RTT_T_[ R`N`R RbLbV RfIeL RbLkLkV RiXl[ RVDRG RaC]G RdXa[ RYI^K RYIWKRM RTNVNVR RXNXR R[NXN RVNTNTRVQTQ R[N[QXQ',
	    'PoRNkN R_C_[ RVFXK R_NhWlY RiEgI R_NVXRY',
	    'PoSRkR R_K_[ RXMZO RfMdO R_QdVlY RbGkL R\\DcG R_QZVRY R^E[HRL',
	    'PoRM\\M R^MlM RXCX[ ReCe[ RWO\\T RSETJ RXMSV R\\E[J',
	    'PoRM\\M Rh[l[ RWCW[ R_EhEh[ RlUl[ RWO\\S RSETJ RWMRW R[EZJ R_E_T[[ R_N^X',
	    'PoRM\\M Rd[k[ RWCW[ RdEd[ RWO\\T RlWk[ RSETJ RWMSW R[EZJ RjD^F RgO]P RiN`P RlNcO',
	    'PoRM\\M R]OfO RWCW[ RbEb[ RlWl[ RYP[T RWMRW RSETJ R[EZJ R]EiEiUjZ',
	    'Po\\JlJ RRM\\M RWCW[ RgWk[ RWN\\S RSETJ R^SgW RWMSW R[EZJ RhNeUbX\\[ RbD^T RiJfU',
	    'PoaHlH RRM\\M R_[k[ RWCW[ RlWk[ RWO\\S R`MjM_X RSETJ RWMRW R\\EZJ RbC^M',
	    'PoSIkI RSTkT R_C_O RWDYG RgDdG R_HbLjO RcQhR R_H[LSP R`UcXfZk[ R_Q]VZYR[',
	    'PoRM\\M R_YlY RXCX[ R_J_[ ReCeX RlJl[ RWN\\T RSETJ RXMSW R\\EZJ',
	    'PoRM\\M R]OlO R`[d[ RWCW[ ReKe[ RWO\\S R_FkEeK RSETJ RWMSW R\\FZJ',
	    'PoRM\\M Rd[h[ RXCX[ RWO\\T RSETJ R`JcPgUlW RXMSV R[EZJ R_EkEjRi[ RdEdKbR`W\\[',
	    'Po_IlI RRM]M Rd[i[ RXCX[ RiCi[ RWO\\T RSETJ RaNcT RXMSV R\\E[J',
	    'Po^IlI RRM\\M RXCX[ RWO\\T RSETJ R`NcUgYl[ RXMSV R\\EZJ RfPdVaY\\[ R`LbT RgCgLeT',
	    'PoRM\\M R^NlN RXCX[ ReEe[ RWO\\T RSETJ RXMSV R\\E[J RjD^F',
	    'PoRTlT R_M_[ RXOZR RfOcR RYFaGeLlO R`TcWl[ RaG]KTO R]T[WR[ R[CWHSJ',
	    'Po^McM RRM[M ReZlZ RWCW[ R^D^Z ReDeZ RlWkZ RWO[T RSETJ RWMRW RZEYJ RkKeN RcY[Y',
	    'PoRL\\L RWCW[ RdUjZ RWNZQ[T RhTbZ RSETJ RbNfW RjNfW RWMRW R[EZJ RbLdU R_LkLhT R_E_S^Z RjD`E',
	    'PoRM[M Re[i[ RWCW[ RWO[S RaQ\\U RdV^[ RSETJ RgMbX RWLRW R[EZJ RbC]M RdH_S RhHeT R`IlIlPj[',
	    'PoRM\\M R`SdS RWCW[ RdCdR RWO\\T RSETJ RiHlO RWMSW R\\EZJ RiQhTdX][ R`H^O',
	    'Po\\HkH RRM\\M Rh[l[ RWCW[ RcCcH R_MhMh[ RWO\\S RSETJ RlVl[ RWMRW R[EZJ R\\ZZ[ R_M_S^X',
	    'PoRL\\L R]ZlZ RWCW[ R^EjEjN RWN[R RSETJ RWMRW R\\EZJ R^OiNiShY RcEbM`Y',
	    'PoRM\\M Rc[h[ RWCW[ RWO[S RSFTJ RgEjM RWMRW R[EYJ RbE^M R^OiOiTh[ RcOaW\\[',
	    'PoUG^G RaMkM RRSlS RUCUM R_N_[ RbCbM RXOZQ RfObS RlJkM R`ScVlZ R]S[WRZ RjFaG R^LUM',
	    'PoRM\\M R]TlT RWCW[ RdOd[ R^GiGiNlNmL RWO[R RSETJ RWMRW R[EZJ RdDaL^O',
	    'Po_KfK RRM\\M R`[e[ RWCW[ R^DfDfJ RlDl[ RWN\\S RSETJ RWMRW R[EZJ R_K^QfQeZ',
	    'PoRM\\M Rc[l[ RWCW[ RcFcZ RkXl[ RWO\\S RSETJ RWMSW R[EZJ RiD]G RjK^L RkQ^S',
	    'PoRM]M R`NjN RXCX[ R`EjEjN RdSk[ RWN[Q\\T RSETJ RaNdU RXLSV R\\E[J R`E`T\\[ R`O_X',
	    'Po]KlK RRM^M RXCX[ RhWl[ RcVfY RXO]T RaU\\[ RUTRW RSETJ RfPjY RXLTU R]E[J RcP_X RdCdLhW RdIbT',
	    'Po^ElE RRM\\M R_QkQ RXCX[ R_JkJkS RWO\\T RSETJ RWMSW R\\EZJ R_J_S^Y',
	    'PoRL[L R^PkP R^[k[ RWCW[ R^E^Z RdEdP R^EkEkR RlWkZ RVN[S RSETJ RWMRV R[EZJ',
	    'Po]IkI RRM\\M R\\ZlZ RWCW[ RdDdI RWO\\T RSETJ RWMSW R[EZJ R`LaW RhLeZ',
	    'Po]IlI RRM\\M R`QiQ R`ZiZ RWCW[ R`C`[ RiCi[ RWO\\S RSETJ RWMSW R[EZJ',
	    'Po]ElE RRM\\M R_TjT R\\ZlZ RWCW[ R_E_Z R_KjKjS RWO\\S RSETJ RWMSW R\\EZJ',
	    'PoRL\\L R_QlQ R_ZlZ RXCX[ R_H_[ R_HlHl[ RWN\\S RSETJ RXLSV R\\EZJ ReCcH',
	    'Po]HlH RRM\\M R]OkO RWCW[ RdCd[ RWO\\S RgTlY RbS[Y RSETJ RdMhV RWMSW R\\EZJ RdMaU',
	    'Po_LiL RRM\\M R_SiS R\\ZlZ RXCX[ R_D_Z R_DiDiZ RWN[R RSETJ RWMRW R\\EZJ',
	    'PodIlI RRM^M R_ZkZ RXCX[ R_Q_[ RdCdQ R_QkQk[ RXO]T RSETJ R]E[J RXMUTRW',
	    'PoRM]M R_QlQ R_ZlZ RXCX[ R_I_[ RfCfZ R_IlIl[ RWO\\T RSETJ RWMSW R\\EZJ',
	    'Po_HlH RSM[M R`[k[ RWCW[ R`K`Z ReIeV RWO[S RlXk[ RSETJ RWMRW R[EYJ R`C]K RjL\\Q RkLjUgU',
	    'PoSIlI RVLiL RXSgS R`C`[ RVFiFiL RjNj[ RYO\\Q RfNcQ R`UeW R_SZXWZ RUNUTTY',
	    'PoVGhG RTLjL RRSlS RTITL RVDVG R_N_[ R_C_L RiDiG RjIjL RWNYQ RfOcS R`ScVlZ R_R\\URZ',
	    'Po_DlD R_JlJ RRM\\M Rf[j[ RWCW[ RWN[S RSETJ RdJdT`[ RWMRW R[EZJ RdPkOkVj[ R_D^Q]Y RdMbX',
	    'Po]ElE RRM\\M RfRlR R]ZlZ RWCW[ R]K][ RgEgR R]KlKl[ RWO[S RSETJ RWLRW R[EZJ RbEbNaS',
	    'PoSEkE RUNiN RRTlT RUIUN R[E[N R_O_[ RcEcN RUIjIjN RXPZR R`TcWkZ RfOdT R^T[WR[',
	    'Po]IlI RRM\\M R\\RlR RWCW[ R`C`R RhChR RWO\\S RgUlZ R`V[[ RSETJ RWMSW R[EZJ',
	    'PoaIiI RRM\\M RbTiT Rh[l[ RWCW[ R^E^[ RbMbV RbMiMiT R^ElEl[ RWN\\S RSETJ RWLSV R[EZJ',
	    'PoSFZF RRTlT R_O_[ RXPZR R]GkFiJ RfPdR R^E[I RcIfLkO R`TcWkZ RaL[O R]T[WR[ RcFbK RZKRM',
	    'PoRLZL R[NlN RVCV[ RXPYS RSETJ RVKSV RYEXJ R^DbDb[_[ ReDjDj[h[ R^D^S\\Y ReDeTd[',
	    'PoWJgJ RUOiO RRRlR Ra[g[ RUEUO R_F_N RUEiEiO RZG[H RdGcH R`JXM R\\JaKfM RYRWVhVg[',
	    'PoSKXK RfKlK RZMdM RR[W[ Rf[k[ RRDXDXK R_C_[ ReDlDlK R_PdU R_MZW R[F\\K RcFbK RfKeQlQlUk[ RSKRQXQXTW[',
	    'PoSHkH R`QkQ R^WlW R_C_N R`Q`W RfNf[ RWTZW RWDZG RXORU RgDdG R_HbJjN R_H]JTN RWR]Q[UXYS[',
	    'Po^HlH RRM\\M RaPkP R_ZlZ RWCW[ ReCeH RfJfZ RWO[S RSETJ RWMRW R[EZJ R^H^R]Y',
	    'Po^ElE RRM\\M R_RkR R_ZkZ RWCW[ R_K_[ R_KkKk[ RWO\\S RSETJ RWMSW R\\EZJ ReEcK',
	    'Po]GhG RRM[M R]MlM Ra[k[ RWCW[ RaPa[ RbCbM RVNZS RkWk[ RSETJ RWMRW RZEYK RjEiHaQ\\R RWKTU RiR`T',
	    'PoSIlI RTQjQ RRVlV RZNZV R_C_N RdNdV RWDYG RgDcH R]JTN RaJgMlN RcXl[ R[XR[',
	    'Po]ElE RRM[M RWCW[ R^K^[ RcJcZ RgKgZ RVO[S RSETJ RWMRW R[EYJ R^KlKl[i[ RcEbO',
	    'PoRM[M R^QlQ R^YlY RWCW[ R^I^[ RcDcY RgDgY R^IlIl[ RWN[S RSETJ RWMRW R[EZK',
	    'PoRM[M R`MhM R^ZjZ RWCW[ R^R^[ R^RjRj[ RWO[T ReFlL ReCfG RbG\\M RSETJ RWMRW R[EZJ RcCaI',
	    'Po^KjK RRM\\M R^PjP RWCW[ R^F^Z RdCdG R^FjFjP ReUk[ RWO[S RSETJ RcPgX RWMRW R[EZJ RkSfU RfY\\Z',
	    'Po^IjI RRM\\M R^MjM R^RjR Rf[l[ RWCW[ R^D^R RgRg[ R^DjDjR RlXl[ RWO\\S RSETJ RWMSW R\\EZJ R`W^ZZ[ RaR`X',
	    'PoRM[M R\\TlT R`[d[ RWCW[ RdQd[ RdGdK RWN[R RSETJ RjGlK RWMRW R[EZJ R`E]K R_MjMhPdQ RiDaE',
	    'PobNfN RRTlT R_P_[ R\\DgDgM RXPZS RWKRO R`J[O RiGkL RTCXD RRGWG RaVkZ RfPdT R[GZK RaF^M R\\VR[',
	    'PoZE_E RaFjF RSTlT RZCZH R_P_[ RUJYL RXQZS RgPdS RWHSL RbHfMlO RbVkZ RWI^IZMSP RjGfL`N R\\VS[ RbFcI',
	    'Po]ElE RRL[L R]MkM R]RkR RWCW[ R]I]R R]IkIkR RWNZQ[T RSETJ RWMRW R[EYJ RcU`YZ[ R^TbXeYk[ RdEdRcV',
	    'Po^JkJ RRM\\M R^PkP R^TkT R[ZlZ RWCW[ R^E^P RdEdZ R^EkEkP RWO[S RSETJ RWMRW R[EZJ',
	    'PoXGcG RWKgK RUQiQ RRSlS Ra[g[ RUEUP R_G_O RUEiEiP RYG[J RdHcJ RWWhWg[ RXSWW R`KXO R^LfN',
	    'Po_JhJ RRM[M R\\NlN Ra[d[ RWCW[ RdNd[ RfTkY R]Q`S RWO[S RjPhS RSETJ RdOgV RWMRW R[EZJ RaV\\Y R`C^K R`FiFgN',
	    'PoRM[M R\\QiQ Rf[j[ RWCW[ RcJcZ RbQgV RVO[S RcQ\\X RSETJ R^K_O RgKfO RWMRW R[EYJ R`C]J R_HlGlTj[',
	    'Po]GlG R`LhL RRM[M R`PhP R\\TlT RWCW[ R`C`T RhChT RgVl[ RVOZS R`V[[ RSETJ RWMRW R[EYJ',
	    'Po]GkG RRM\\M R\\SlS RWCW[ RdQd[ RWOZR R`LbN RSETJ RWMRW R[EZJ R`I^O RdCdG RhIeP RgLjO',
	    'Po^KkK RRM\\M R^PkP R\\UlU RWCW[ R^F^O RfRf[ R^FkFkO RWO[S RdFdP`T RdCcF RSETJ RWMRW R[EZJ',
	    'Po_EjE R_HiH R_LiL RRM\\M R\\PlP RWCW[ R_E_Z ReUjZ RWO[S RiTeU RSETJ RcPfW RWMRW R[EZJ ReY\\Z',
	    'PoSHbH R\\QbQ RZWcW RZCZN R\\Q\\W R`N`[ RUDWF RXVS[ R`D^G RgOlY RZIaL RSTXV RVOST RURZRWY RZHVLRM RcNg[ RfDcO RjDgO',
	    'Po_KiK RRM[M R\\PlP RWCW[ R]F]J RdCdG R]FkFkJ RWO[S RSETJ RiSkY RWMRW RZEYJ R_S\\Y RdPd[`[',
	    'Po^FkF R^IkI R]LlL RRM[M R_SjS R_VjV Rf[j[ RWCW[ R_O_[ RdCdL R_OjOj[ RWMXPZT RSETJ RZEYJ RWLSV',
	    'Po^JkJ RRM\\M R^NkN R\\RlR RWCW[ R^E^N RdEd[ R^EkEkN RWO[T RfSlZ RdQ\\Y RSETJ RWMRW R[EZJ',
	    'Po\\GkG RRM[M Re[l[ RWCW[ R`M`[ R`C`I RfKf[ RgCgI RlWl[ RVOZS R`M[R RfRbS RjMeR R`K^O RSETJ RWMRW RZEYJ',
	    'PoaHjH R^HgH R\\LkL RRM[M RWCW[ RWMXQZS RfLkP RfEjI RcJ]P RcC^H RSETJ RWMRW R[EYJ RcHaM RcQ^S RfS_W RiV_[',
	    'Po\\FlF R_JiJ RRM[M R_MiM R[QlQ RfVkV R]ZlZ RWCW[ R]Q][ R_C_Q RgQgV RiCiQ RWO[S RSETJ RWMRW RZEYJ RbQaW',
	    'Po^HjH R^LjL RRM[M R\\OlO Rf[j[ RWCW[ R^D^L R^DjDjL RVNZR RaO[U RbT\\Z RfWb[ RSETJ RgRdY RWMRW RZEYJ R^SkSj[',
	    'Po]EdE RRM[M R]MlM R^VlV Ri[l[ RWCW[ R]E]R^[ RcQc[ RgQg[ R]HlHlM R^QlQl[ RWPZS RSFTJ RWMRW RZEYK R]M[Y RkDcE',
	    'Po^DjD RRM[M RbOeO R\\SlS RWCW[ RdPd[ ReHeO RWO[S RdRkY R]IkIiM RdR]Y RSESJ RaGgI RWMRW RZEYJ RiEdG RdIaL\\N',
	    'Po\\ElE RRM[M RbOfO RbUfU R]ZkZ RWCW[ R]J][ RbJbZ RgJgZ R]JkJk[ RWPZS RTETJ RWMRW RZEYK RcEbN',
	    'Po`JlJ RRLZL RaOkO R_TlT RVCV[ R^G^[ RbEiEiJ RgWk[ ReV`[ RSETJ RVMXR RfRiY R_C[K RbK`R RVKSV RYEXJ RePeW',
	    'PodHiH RRMZM RcMlM R^SjS R^WjW Rf[j[ RWCW[ R^O^[ RdDdL R^OjOj[ R\\ElEkG RVOZS RSETJ RVMRW R_G\\N RVKTU RYEYK',
	    'Po]LjL RRM[M RWCW[ R]D]K RjDjK RWO[S RaM]Q RbP\\V RgDaJ RSETJ R`TcXj[ R`EfH RfLfPkOkP RWMRV R[EYJ R_TiSeWaY\\[',
	    'Po[IdI RfKlK RSLZL RfRlR R\\XcX Rh[l[ RVLXQ RVCV[ R\\P\\Y R_C_O R\\PcPcW RfDlDl[ RSETJ RZEXJ RVMSV RfDfTdZ',
	    'Po\\HlH RRMZM R]PcP R]TcT R`[c[ RWCW[ R]L][ R]LcLcZ RfLfV RVNZS RSETJ R_C`F RUPRW RiCgH RVKSV RZEYK RkKk[g[',
	    'PoRM[M R^UjU R^ZjZ RWCW[ R^Q^[ RgCgM R^QjQj[ RXPZT RhGkJ RbJ]O RSETJ RWMRW R[EYJ R_C\\I R^FdFaL R^G`KcNjO',
	    'Po[HlH RRMZM R\\ZbZ RdZjZ RZZ`Z RWCW[ R[O_T RXQYT RcOgT RSETJ RaVb[ R^C`G RjVl[ RjNdZ RWMRW R_J\\P RaN\\Z RhCfH RgJdP RZEYK',
	    'Po]ElE R`MkM RRM[M R`QkQ R_UkU R^[l[ RWCW[ R`H`Q ReHeZ R`HkHkQ RWO[T RSETJ RWMRW RZEYJ R]E]S[Z',
	    'Po]IkI RRM[M R`SkS R`WkW Rh[k[ RWCW[ R`P`[ ReKeO R`PkPk[ R]DkDkI RWO[S RiLlN RbL_N RSETJ RWMRW RZEYJ R]D]Q\\Z',
	    'Po]FlF R^LkL RRM[M R^OkO RhRjR R\\TlT Rd[g[ RWCW[ R^I^R RdCdQ RgRg[ R^IkIkQ R`VbY RYPZU RSETJ RgDjD R[EYJ RTPRX',
	    'Po]JcJ RfJlJ RSL[L R^[a[ Rg[k[ RWCW[ R]DcDcJ ReDlDlJ RfJeOlOlY R^Q`S RVNZR RfQhS RSETJ RWMRW RZEYJ RbT\\W RlTeW R]J\\OcOcVb[',
	    'Po\\FlF R]JlJ RRM[M R`RkR R`VkV Ri[k[ RWCW[ R`N`[ RaCaI ReNe[ RgCgI R`NkNk[ RWPZS RSFTJ RWMRW RWKTT RZEYK R]J]S[Z',
	    'PoSF_F RSI^I RiMlM RUN\\N RRT^T RXOX[ RYCYI RSLSM^L^O RbEiEiL RlJlL RTPUS RXTSZ R\\P[S RbQdUhYl[ RXU]W R`PjPhUdY_[ RbFaL',
	    'PoRM[M R^UjU R^ZkZ R\\ZfZ RWCW[ R^P^U RdMdZ R^PjPjU RhWl[ RWOZS RcFdG R^H]K RSFTJ R_FcKkN RWMRV RZEYJ R]EjEeJ]N',
	    'Po]HkH RRM[M R^MkM R\\SlS RWCW[ RbVb[ RdHdS RWN[Q RSETJ RaDaH RjVl[ RWMRW R[EYJ R]V[[ RhCfH RfVf[',
	    'Po]FlF RRM[M R^NlN R_QjQ R`ZjZ RWCW[ R`U`[ RdCdG RdHdQ R`UjUj[ R_JjJjQ RWO[T RSFTJ RWMRW RZEYJ R]F]R[Y',
	    'Po^IjI RRM[M R^MjM R\\UlU RWCW[ R^FjFjP RWO[S RcCbF RSETJ R^F^PhQjS RdSeWgYl[ RWMRW R[EZJ RcV`Y[[ RdQcW',
	    'Po^KkK RRM[M RWCW[ R]F]I RdCdG R]FkFkI RWO[S RhTlX RjNgR RSETJ ReMiV RWMRW RZEYJ RdKaN\\P RbR\\T RdSaW[Y Rd[a[ RaNdReWe[',
	    'PobHkH RaLlL RRMZM RbWjW Rb[l[ RWCW[ RbPbX RfCfO RbPjPkX R\\J^M RXQYT R\\E_G R[Q_Q_X[[ RSFTJ RWMRW RZEYK RcDaJ',
	    'Po\\FlF RRM[M R\\PaP RbPlP R[TaT RcZlZ RWCW[ R\\K\\Q R`C`H RaJa[ RgJgZ RgCgH RWPZR RSETJ RWMRW RZEYK R]T\\Z',
	    'PoRM[M R_QiQ R]UkU R\\[l[ RWCW[ R\\H\\L R_L_Q RdQdZ RdCdH R_LiLiQ R\\HlHlL RWO[S R^D_G RjDhG RSETJ RWMRW RZEYJ',
	    'PoTFlF RWI_I RbIkI RVUlU RXKWP R[G[P R^K^O R`C`F RaQa[ RfGfP RZR\\T RgReT RbVkZ RbKbP RaT^WV[ RjKiPkOlP RTFTSR[',
	    'Po\\HjH RRMZM ReMlM RWCW[ RXQYT RjKlM RgEjH RbD^G RSETJ RdKhOlQ RhIgN RWMRW R^I\\M RcK^P[Q RcP^S RfS^W RZEYK RiVcY\\[ RbM\\M',
	    'PoSGkG RVOhO RVRhR RSTkT RRWlW RVLVQ RZRZW R_C_J R_L_Q RdRdW RVLhLhQ RWDYF RgCdG R_GeJlK R_GYJRL R[YR[ RcYkZ',
	    'Po\\FlF R]MkM RRM[M R]QkQ R^WjW R^ZjZ RWCW[ R]I]P R^S^[ RbCbP RfCfP R^SjSk[ R]IkIkP RXPZT RSETJ RWMRW R[EXJ RWKTT',
	    'Po\\GlG RRM[M R]NlN R_QjQ RWCW[ RdCdF R_JjJjQ R_SaU RWNYT RjTfV RSETJ RWMRW RbX\\Y RZEYJ RdHd[a[ R\\G\\R[Y RdQgVjZ',
	    'Po^FkF R]KlK RRM[M R_QjQ R_TjT Rg[l[ RWCW[ R_M_T RgTg[ R_MjMjT RlXl[ RdCdF RWMYQZT RSETJ RaGaI RWMRW R[EZJ RhGgK R`Z[[ RbTaY',
	    'Po]FlF RRM\\M R^NjN R^QjQ R\\UlU RWCW[ R^J^Q RaCaH RgCgH R^JjJkQ RWN[S RSETJ RdTeWgYl[ RWMRW R[EZJ RbX\\[ RdRbX',
	    'Po]DcD ReDkD RdJlJ RRMZM R^VjV R^ZjZ RWCW[ R^R^[ RdFdJ R^RjRj[ RiKiPlOlP RVNZR R_L[P RSETJ RWMRW R\\F\\JbJbO`O RZEYK R`F_L RhEgL',
	    'Po[GcG R[KdK RRMZM R\\RbR R\\VbV R`[b[ RWCW[ R\\N\\[ R]C]K RaCaK R\\NbNb[ RXQYT RSETJ RgRkZ RiQeZ RWMRV RfGfMiX RYEYK ReJhV ReJkJhV RfCdO',
	    'Po\\DkD RRLZL R\\PlP Rh[k[ RWCW[ R]S][ R]H]N RbSbZ RbHbM RfHfM RfSfZ R]SkSk[ RVNZR RbPbV RSETJ RWMRW RZEYJ RcEbJ R]HjHjNhN',
	    'Po]ElE R^IkI RRM[M R^OjO R[SlS R^ZjZ RWCW[ R^V^[ R^K^O RdCdI R^KjKjO R^VjVj[ RVM[Q RTETJ R`P`S RhPgS RWMRW R[EYJ',
	    'Po^GjG R^JjJ RRMlM R]RjR R]UjU R]XkX R[[l[ RWCW[ R]O]U R^D^J RdOd[ R^DjDjJ R]OjOjU RWO[T RSFTJ RWMRW RZEYJ',
	    'PocJjJ R\\K`K RRMZM RcTkT RbZlZ RWCW[ R\\D`D`J RcNcT RcDcJ RgJgZ RcDjDjJ RcNkNkT RWPZS RSFTJ RjVl[ RWMRW RZEYJ R_[\\[ R\\J\\Q`Q`U_[',
	    'Po`IgI RRM[M R]QjQ R^WiW R^ZiZ RWCW[ R]L]Q R^T^[ RdLdQ R^TiTi[ R]LjLjQ RYQZT RdCiHlJ RcC]I RSETJ R`M`O RgMfO RWMRW RZEZK',
	    'Po`FkF RTH]H R_JkJ R_MkM RUO]O RRTlT RTDUKUO RTD]D]H RUK]K]O R_P_[ ReJeP ReCfF RYQZT RfQcT RbGbI RaVkZ RhGgJ R\\VR[ RTFSN',
	    'Po]IkI R\\LlL RRM[M R^SjS RWCW[ R]D]H R^N^S R`V`Z R^NjNjS R]DkDkH RWO[S RSETJ RdSeWhZl[ RWMRW RZEYJ ReSbT[X RkUfW RaDaI RfDfI RfZ][',
	    'Po]IkI R^LhL RRM[M R\\PlP R`VjV R`ZjZ RWCW[ R]D]I R`S`[ RbDbIcP RfDfI R`SjSj[ R]DkDkI RVN[S RSETJ RWMRW RZEYJ RkKfP\\U',
	    'Po^LkL RSMZM RWCW[ R^D^L RkDkL RXQYT RaM]Q RSETJ RgNlP RcP_TdWk[ RdIfJ RaEgGiG RcDeE RVMRW RhDdH`I R`SiSdX][ R`GbG RVKTU RZEYK',
	    'Po\\DcD RfHlH R\\LcL RRM[M R\\PdP R[[l[ RWCW[ R\\D\\P R]S]Z RbSbZ R\\HcHcL RfSfZ R]SkSkZ RVOZS RSETJ RhJjO RZEYK R`L`P R`D`H RVKSV RgCeL',
	    'Po]EkE R^KbK RfKjK RRMZM R]MbM RfMjM R\\PlP RWCW[ R\\H\\K R]T][ RbSb[ RdEdN RfTf[ R\\HlHlK RVOZS RSETJ RWMRW RWKTU RZEYK R]TkTk[i[ RcPbW',
	    'Po^HjH R^LiL RRM[M R^OiO R^RjR R^TkT RdXgX R\\ZlZ RWCW[ R\\E\\[ R^J^O RdFdQ RgRgX R^JjJjO R\\ElEl[ R`UbW RWPZS RSETJ RWMRW RZEYK',
	    'Po_FkF R^JlJ RUL\\L R`NkN RUQ]Q RRUlU RUIUQ RSJ\\J\\L RTDTF]E]F RUN]N^Q_[ ReJeQ RXR[U RdDfF ReRcU RaGbI RhGfJ RXDWHRI R`VfYlZ R]VWYRZ',
	    'Po\\ElE R^HlH RRM[M R`NkN R`QkQ Rh[k[ RWCW[ R_T_[ R`K`Q RbFbJ ReKeX RhFhJ R`KkKkQ R_TlTl[ RYQZT RSETJ RaXiWiY RWMRW RZEYK R\\E\\S[Z',
	    'PoSF`F RSI_I RiIkI RRK`K RTQ^Q RRUlU RYIYQ R_R_[ RdDiDiI R^D]F R[C[F\\I RcGaJ RWCWFVI RbMkQ RWS[T RbKjKgOaQ R_UeXlZ RTMTPWOTO R[M^M^P[O RhRbT R_UYXRZ RcDcI RUDTF RWMTM',
	    'Po_QkQ RRT]T R_TkT R_WkW R_ZlZ RXMX[ R`P`[ R^DcDdL RfOfZ ReDkDkM RYU\\X RWS[W RhFfI RiIfM RXFVIRM RaF^I RbJ^M R[OZR RXTRY RTOUQ RTEXE[K R`M^S',
	    'PoRElE R_IkI RTL\\L R^MlM R_PkP RTQ]Q RRVlV RTITR RZCZG RTI\\I\\L RTN]O\\R R_R_[ RdCdGeI ReMeR RXSZU RfSdU RhJgM RbJbM R_VeYl[ R_VYYR[',
	    'PoTH[H RSM\\M R_QkQ RRT]T R_TkT R_WkW R_ZlZ RSJSM RTETH RXCXM RXNX[ R[E[H R\\J\\M R`O`[ R^DdDdL RfOfZ ReDkDkL RYU\\X RWS[W RhFfH RiIfM RaF^H RXSRY RTOTR R[OZR RbJ`M^T',
	    'Po\\ElE R]KcK ReKlK RRMZM ReNlN R]NcN R^PjP R_ViV Rh[l[ RVCV[ R]I][ R`C`G R]IcIcN RdOd[ ReIeN RgCgG R_RiRiV ReIlIl[ RdUhY RVNZS RdU`Z RSETJ RYEYK RWMRV R_R_V',
	    'PoRQjQ R_Q_[ RfUkZ ReLlS RYTSZ R^DWJ\\L RdGVQ',
	    'PoRL[L R[DRL RXNTR R_HWP R`UbY RTUS[ RZU[Z R[QRR R]QUR R_NaRYR',
	    'PoRO^O Rb[k[ RYOY[ RbDbZ R[L^O RRHWL RVESH R[GYJSO R\\S^W RTSRY RlVk[',
	    'PoRRjR R_R_[ ReUgVlZ RfNkT RXUSZ RjD]FTF R\\FWL]N RdHZR',
	    'Poe[j[ RWTWZ RaW][ RVGRK RZJRR R[T\\Y RXCUI RcR_Z RSTRZ R^JlJlTk[ RdCdLbV RYKRL RZM\\QRR',
	    'Po^FlF Ra[f[ RXTYZ RfFf[ RXCWGRK R[IRR R\\T]Y RSUR[ RYKRL R[N]QRR',
	    'PoRP]P RXPX[ RaEaT RiCi[ RSHWL RWCVFSH R[GYJSO R[S]W R\\M]Q RTSSY RjS`T',
	    'Poe[j[ RWFRK R[IRR R\\T]Y RXDVH RcR`Z RSUR[ RXTXZ R_FlFlSj[ RdFdLbV RYKRL R[M]QRR',
	    'Po`NjN RRP]P R`[k[ RXOX[ RaNa[ R_EjEkO RSHVL RlWk[ RVESH R[GYJSO R[S]W R\\M]Q RTSSY',
	    'PoWTWZ RcSjZ RcIdM RUHRK RXLRQ R[T[X R^IdU RWCUJ RjHfS_Y[[ R^FaQ R]FkFhP RSTRZ RZHXLRL RZM[RRR',
	    'Po]JlJ RRP\\P Rc[h[ RXOX[ RhCh[ RZFYJVL RSHWLSO R[S\\W R[L\\Q R`NbT RVCTI RTSRY',
	    'PoRP\\P RXOX[ RfFfX RlDl[ RSHWLSP R[R\\V RXJWM RZL\\Q RWCTI RZGXK RTRSY R`E`Q^X',
	    'PoRP[P Rd[j[ RWPW[ RTIWKSP R[S]W RZL\\Q RcOeT RVCTI RZGWK RbC^L RTSRX R`IlIlRj[',
	    'Po^FlF RRO^O R]ZlZ RYOY[ ReFeZ R[L^O RSHWL RVESH RYJSO R\\S^W RTSRY',
	    'Po_ElE R^NlN RRP]P R`[e[ RXOX[ ReEe[ RSHVL RVESH RYJSO R[S]W R\\M]Q RTSRY',
	    'Po_HkH RRP]P R_[l[ RXPX[ R_MiM`U_Z RlWk[ RZGYJ RSHWKTP R[R]W R[M]Q RWCTI RTSRY RaC^J',
	    'PoRP[P RWOW[ RlWl[ RcRfT R_W\\[ RSHVKVMSP RZS\\W RYL[Q R]OdR RVCTI RYGWL RTSRY R\\IiIiUjZ RbCbM`V',
	    'PoRP\\P Re[j[ RXOX[ RbU][ RSHWLSP R[R\\W RZL\\Q RVCTI RZGXK RdP`X RTSRX R`K^R R^FlFlTj[ ReGcU',
	    'PoRFlF RSUjU R_C_F R_U_[ R\\OXQ RdRlV ReWlZ RWP]S RcO`QYU RZWRZ RYH`KiN RfFdI^KTN R_KlN R`KRN',
	    'Po\\IlI RRP\\P RXPX[ RdCdI RTIWLSO R[R\\W R[L\\Q RVCTI RZFWL RiJeT`Y[[ RTSRY R_IaQeVk[',
	    'Po]JlJ Re[k[ ReNeZ RgEjH RUHRK RZHXLRR RZT[X RWCUJ RbR^Z RlVk[ RSURZ RWUXZ RcCcLaV RXKRL RZN[QRR',
	    'PoRP\\P Rh[l[ RXOX[ R^I^[ R^IlIl[ RTIWLSP R[R\\W RZL\\Q RVCTI RZGWL RTSRY ReEeMaS ReNjS',
	    'Po\\KkK RWTWZ RfUl[ RfEiH R`V[[ RVHRK RZHXLRQ RZTZX RcCcOhX RWCUI RbQ^Y RcKfU RSUR[ RcM`W RcIdR RcJaU RXKRL RYN[QRR',
	    'PoRP]P RXOX[ RhDh[ RSHWL RWCVFSH RYISO R[S]W R\\M]Q R_LeO RaFeH RTSSY RlR^T',
	    'Po]OlO RRP[P R\\ZlZ RXOX[ R^EiEiZ RSHVL RWLSP RZR\\W RYL[Q RVCTI RYFWL RTRSY RbFbLaQ`Z',
	    'Po]FlF RRP[P RWOW[ ReJe[ RfKlQ RdJ]Q RSHWLSO RZS[W RYL[Q RWCTI RZFXK RfFcL RTSSY',
	    'PoRP\\P RXOX[ RgLg[ ReElL RSHWJWLSP RcCbF\\L RZS\\W RZL\\Q RVCTI RZFXK RSRRY R`L`S_X',
	    'PoRP[P R_[d[ RXOX[ RdMd[ R^EjEeJ RSHVLSP RZR\\W RZL[Q R`HgK R]NlMiR RVCTI RYGWK RTRSY',
	    'PoRP[P R_SjS Re[l[ RXOX[ R_K_U ReCeZ RjKjU RSHVL RlXl[ RWLSP RZR\\W RYL[Q RVCTI RYFWL RTRSY RjF_G',
	    'Po^LcL RRP[P Rf[l[ RWOW[ R^D^Z RfDfZ RlWl[ RSHVLSO R[R[W RZL[Q RVCTI RYFXK RkJeM RTSRY RcY[Z',
	    'Po_KfK RRP\\P R`[e[ RXOX[ R_DfDfK RlDl[ RZHWK RSHWKSO R[S\\W RZL\\Q RWCTI RTSRY R_K_QfQe[',
	    'PoRP[P RaSdS RXOX[ ReCeR RTIVL RSP RZR\\W RYL[Q RiHlN RVCTI RYFVL R`H]N RiPhUdX^[ RTRSY',
	    'Po\\IlI RRO[O RWOW[ RSHWLSO RZR[W RZL[Q RhSl[ RVCTI RZFWK RTRSY ReLaY RaC`M_S[[ RaZjX',
	    'Po_NlN RSP\\P RXPX[ R_F_Z RSHWLSP R[S\\W R[M\\Q RVCTI RZFWL RTSRX RjD_E RfY]Z RfEfPi[mX',
	    'PoRP[P RXOX[ RZR[W RTIWKSP RZL\\Q RVCTI RYFWK RfLkLhTcY_[ RTRSY R\\EgEfL R_E_P^T\\[ R_LdTk[',
	    'PoRP\\P RXOX[ RTIVLSP R[S\\W R[FVL RZL\\Q RgCkM RWDTI RaC]M RSRRY Rh[d[ R^NjNjTh[ RcNaW][',
	    'Po_FkF R^NlN RRP\\P RaZkZ R]ZhZ RXOX[ RSHWLSP R[S\\W RZL\\Q RhTl[ RVCTI RZFXJ RTRSY RcN`Z',
	    'Po]OlO RRP[P R^ZlZ RXOX[ ReFeZ RSHVLSP RZS\\V RZL\\Q RVCTI RYFWL RTSRY RjE^G',
	    'PoRP[P Re[l[ RXOX[ R]H]L ReCeZ R]HlHlL RlVk[ RaV\\[ RSHVLSP RZS\\W RWJVM RZL\\Q RVCTI RYFWK RcQ`X RTSRY RdIbU',
	    'PoWUWZ RcKeM RdDlL RUHRK RWMRR RcF]L RZT[Y R^QjQe[ RWCUJ RdCaH RSUS[ RZHXLRL RZN[RRR',
	    'PoTFkF RVIiI RRLlL RSUjU R_C_L R_U_[ RcNYU R]LYQ RdRlU ReWlZ RXQ]S RZWRZ',
	    'Po]HlH RRP\\P Rd[i[ RXOX[ RdCdH RSHWLSP R[S\\W RZGWL RZL\\Q RVCTI RTSRX RbNjNjTi[ RbIaQaU][',
	    'PoSFlF RRTjT RSJSN R_C_J R_T_[ R]KYO RdQjT RYO]R ReWlZ RdM`P]QXT RYWRZ RSJlJlN',
	    'PohNlN RRP[P R]TlT RWOW[ RdOd[ R^GhGiN RlKlN RRHVLSO RaK]N RZS[W RWJVM RZL[Q RVCTI RYFXK RTSRY RcCaK',
	    'Po`LlL RXUXZ RhLh[ RVHRK RXKRR R[T\\X RXCUI RSURZ RkD`F`Q^Y R[GYKRL R[M[QRR',
	    'PoSF_F RbNkN RUNYN RVUdU R[[_[ RYCYN R_U_[ RcCcM RZUaUhTkV RlJkN ReRiT RdWlZ R_MVQ_RTU RcOXT RZWR[ R[ITJ R^IWJ',
	    'Po^OkO RRP\\P R_[k[ RXOX[ R_E_Z ReEeO R^EkEkQ RYIVL RSHWLSO R[S\\W R[L\\Q RVCTI RZFXJ RlVk[ RTSRY',
	    'PoUQ`Q R]UjU RZUfU RSUbU RZ[_[ RSESL RYDYM R_U_[ ReRjT RdWkZ R]NVQ RYWRZ R_FdKlM R^EjEfIbK\\L RdPRU',
	    'PobJlJ Re[i[ R`G`[ RiCi[ RUHRK RWMRQ RZTZX RdNfT RWCTJ RaD\\N RWTWZ RSUR[ RYHWLRL RYNZRRR',
	    'Po_IjI R]PlP RWTWZ RfVk[ RbU][ RVHRK RWLRR RZT[X RdCdQhY RdPaX RWCUJ RSUR[ R`E^M RdLcU RYHXLRL RZN[RRR',
	    'Po`LdL RRPZP Rf[l[ RVOV[ R]J]Z R`C`Y RgCgZ RlWl[ RRHVK RjLfM RkJiL RYRZW RXLZQ RVCTI RXFUN RSSRY RdY[Z',
	    'PoYF_F RbLlL RRUkU RTETL RYCYL R_U_[ RbCbL RlIkL R^NXP RgTmV RWP]R RdWlZ RdOXU RYXRZ RjEaG R]KSL',
	    'PoRP\\P R^QlQ R^YlY RXOX[ R^I^[ ReCeY R^IlIl[ RTIVLSP R[R\\V RZHWL RZL\\Q RWDTI RTRSY',
	    'Po]KbK RRPZP R][`[ RVOV[ R\\DbDbK RRHVK RYRZW RXLZQ RVCTI RXFUN RSSRY R\\K\\QbQbVa[ RgDfJfMdZ RiQlZbY',
	    'PoRF_F RbNkN RSUjU RYCYN R_T_[ RcCcM RlJkN RYFRM R\\MXQ RXFZH^J RdRkU RdWkZ RVP\\S RbO_RXU RYWRZ',
	    'PoTHjH RTMjM RRUjU RTDTM R_U_[ R_D_M RTDjDjM R]MXP RdRlU ReWlZ RWP\\S RdOWU RYWRZ',
	    'Po_OlO RRO]O R_YlY RXOX[ R_F_[ ReFeY R_FlFl[ R[HWL RTIWKSO R[S\\W R[L]Q RWCTI RTSRY',
	    'Po\\IkI RRP[P RXOX[ ReUjZ R^WZ[ RgUa[ RSHVLSP RZR\\W RWJVM RYL[Q RaCaOfW ReDjF RiNeW RVCTI RYFWL R`Q\\Y RaIdT RiLgU RTRSY RaK_V',
	    'Po[KlK RRP[P RcShS R^YlY RWOW[ R^E^[ RcDcT RiDiT RRHVLSO RZS[W RZL[Q RVCTI RYFXK RTSRY',
	    'Po^NlN RRP\\P R^TlT RXOX[ R^H^U ReCe[ R^HlHlU RSHWLSP R[R\\V RZL\\Q RVCTI RZFWL RTRSY',
	    'PoWTWZ ReCeN RUHRK RYHWMRQ RYTZX R`NbTgYl[ R^HlHkL RWCUJ R^NjNhTcY^[ RSUR[ RXKRL R^H^R]Z RZN[RRR',
	    'Po]MlM RRP[P RXOX[ R]G]K RdCdG R]GlGlK RSHVLSP RZS\\V RWJVM RZL\\Q RVCTI RYFWK RTSRY RdMd[`[',
	    'Po^IkI R]PlP RWUWZ RUHRK RWMRR RZT[X RWCUJ RdCdP_Z RSUR[ RZHXLRL RZN[RRR RiUkZ^[',
	    'PoWTWZ RbWf[ RcKeN RdElL R_PjPhTcX RUHRK RWLRQ RdCbG]M RZT[X RWCUJ RSTRZ RZHXLRL RZN[RRR',
	    'Po^IlI R_QkQ R][l[ RWUWZ ReIe[ RdCeG RVHRL RXLRR RXCUI RSUR[ R[T[Y RZHXLRL R[N\\RRR',
	    'PogNkN RRP\\P R_YkY RXOX[ R_Q_[ R_QkQl[ RSHVLSP R[R\\V RZL\\Q RVCTI RZFWL RTRSY R^ElEkM RdEbJ_N',
	    'Po\\IlI RRP[P R_QiQ R_YiY RXOX[ R_C_[ RiCi[ RSHVLSP RZR\\V RYL[Q RVCTI RYFVL RTRSY',
	    'Po]QlQ RWTWZ R_H_Q R_HjHjQ RfVk[ RdU^Z RZHXLRQ RZT[X ReCeRhY RWCTJ RSUS[ RYN[RRR ReMdV RXKRL',
	    'Po^MkM RRP[P RWOW[ RgCg[ R]GkGkM R`V\\[ RSHWLSO RZR[W RYL[Q RVCTI RZFXK RTSRY RkYiY R^M]SlRkY RbCbPaW',
	    'PoRP[P Ra[k[ RXOX[ R]G]K RaKaZ R]GlGlK RlWk[ RSHVLSP RZR\\W RZL\\Q RVCTI RZFXK ReCeG RTSRY RjO`R',
	    'PoRP[P RWOW[ RdClK RRHVLTP RcE\\L R[R[W RYL[Q RVCTI RYFWL ReKbN_O RgOeR_T RkRfW^[ RSRRY',
	    'Po\\MkM RRP[P R_YjY RXOX[ R_Q_[ R_QjQj[ RSHVLSP RZR\\W RWJVM RYL[Q RiIkO RVCTI RYFWL RcC_M RTRSY',
	    'Po_MkM RRP[P R^YlY RXOX[ R^Q^[ R_F_M ReCeY RkFkM RlQl[ RSHWLSP RZR\\W RXJVM RZL\\Q RVCTI RZFXK RTRSY',
	    'Po_HlH RRP[P R`[k[ RWOW[ RaLa[ ReIeV RlWl[ RSHVLSO R[R[W RYL[Q RVCTI RYGXK RaC]K RTSRY RjMdO]Q RjLjUgU',
	    'Po`GiG RRP[P RXOX[ RSHVLSP RbC`G\\J RZR\\W RYL[Q RaIgOlQ RbRgT RVCTI RYFVL RhHbO]Q R_WjZ RTRSY',
	    'Po\\HkH RRP\\P R\\ZjY RXOX[ RSHWKSO R[R\\W RZL\\Q RVCTI RZFWK RTSRX RdDdH R_OdS RjM^Z RiVlZ',
	    'Po_LjL RRP\\P R_SjS R\\ZlZ RXOX[ R_E_Z R_EjEjZ RSHWKSP R[S\\W RZL\\Q RVCTI RZFWK RTRSY',
	    'PoRP\\P RbThT Rh[l[ RWOW[ R^E^[ RbKbU RbKhKhT R^ElEl[ RZFXJ RSHWLSO R[R[W RZL\\Q RWCTI RTSRY',
	    'Po^LkL RRP\\P R]RlR RXOX[ RdCd[ RSHWLSP R[S\\W RZL\\Q RiEkJ RVCTI RZFWL R`E^J RTRSY',
	    'PoRP\\P R_UgU Re[j[ RXOX[ R_M_V R_MgMgU RSHVLSP RZR\\W RZL\\Q RVCTI RZFXK RaC]L RTRSY R_HlHlSj[',
	    'PoRP]P R_QlQ R_YlY RXOX[ R_H_[ R_HlHl[ RZHWL RSHWKSO R[S]W R[M]Q RWCTI RfCdH RTSSY',
	    'Po\\IkI RRP[P RiYlY RWOW[ RdCd[ RiLiY RlVlY RSHVLSO RZS[W RWJVM RYL[Q RgDkF RVCTI RYFWK RTSRY R_L_Q^W',
	    'Po^IlI R`UjU ReCe[ RVHRK RXLRQ RZT[Y RfKjT ReHgM RWCUJ ReHcL_U RSUR[ RWTWZ RZHXLRL RZN[QRR',
	    'Po^HlH RRP[P RcYkY R_YfY RWOW[ ReCeH RSHWLSO R[R[W RZL[Q RhRl[ RVCTI RZFXK RTSRX RdKaY R^H^R\\Z',
	    'Po]EjE RRP[P R^TkT R\\ZlZ RWOW[ RdOdZ RSHVKSP R[R[W RYL[Q R_EaIeLlO RVCSI RYFVK RiGdL]O RTRSY',
	    'Po`OlO RRP[P RaZlZ RWOW[ R_H_[ RgFgZ RRHVLTO RZS[V R^IZL[Q RVCTI RYFWL RaC]K RTSRY RkEaG',
	    'PodElE RdLlL RRP[P RWOW[ R`L`[ RSHWKVLSO RaD\\I RbI\\O RZR[W RYL[Q RWCTI RYFWK RTSRY RiLi[e[',
	    'Po]LkL RRP[P R]QkQ RWOW[ R]G]Q R]GkGkQ RSHVLSO R[R[W RYL[Q RVCTI RWJVM RYFXK RiSfW][ RTSRX RlWk[i[ RdCdNfX',
	    'Po]GlG R^MlM RRP\\P R_YjY RXOX[ R_R_[ ReCeM R_RjRk[ RSHWLSP R[S\\W RZL\\Q RVCTI RZFWL RTRSY',
	    'Po_IkI R]OkO RRP[P RWOW[ RdCd[ RhTlX RaT\\Y RSHVLSO RZS[W RYL[Q ReOhU RVCTI RYFXK RaD]L RdMaT RTSRY',
	    'PoWUWZ RfDfZ RlDl[ RWMRQ RZT[Y RhKjQ RWCSL RbKcQ RSTRZ R]K\\Q RZNZRRR R`D`R_X RYHWLRL',
	    'Po^GjG R]MlM RRP\\P R^TkT R\\[l[ RWPW[ RdCdM RdOdZ RSHVLSP R[R[W RZL\\Q RVCTI RZFXK RTSSY',
	    'Po]ElE R_RkR R_YkY R_K_[ R_KkKk[ RXLRR R[T\\Y RWCTJ RSUR[ RdEcK RWTXZ R[GYKRL R[O\\RRR',
	    'PoRP[P R_SkS R_[k[ RXOX[ R_L_[ ReLeS R_LkLkT RSHVL RlWl[ RVMSP RZR\\W RZL[Q RVCTI RYFWK RcF`J\\K RTRSY RjJfJ R]EkEjJ',
	    'Po\\NlN RRP[P Rf[l[ RXOX[ RdCdN RgNg[ RlXl[ R`W\\[ RVMSP RZR\\W RSHWKVM RYL[Q R^E_K RjEhK RVCTI RYFWL RTRSY RaN`W',
	    'PoRP[P RbThT R^YlY RXOX[ R^E^[ RbJbS RbJhJhS R^ElEl[ RSHWLSO R[S[W RZL[Q RWCTI RZFXK RTSSY',
	    'PoVUWZ RhWl[ R]F^J RcU][ RVNRR RePiY RePaX RcHkHiM RVCRL RYHVN RdE`N R_Q\\X RSUR[ ReMfU ReLcU RWKRL RYNZRRR RYTYX',
	    'Po]ElE R_OjO RRP\\P R_UjU R\\ZlZ RXOX[ R_J_U R_JjJjU RSHWLSP R[S\\W RZL\\Q RVCTI RZGXK RTRSY',
	    'Po\\ElE RaKjK RRP[P R\\ZlZ RXOX[ RSHWKVMSP RZR\\W RYL[Q RcNgP RaSfU RVCTI RYFWK RTRSY RbE_S RiMhZ',
	    'Po_GkG R]UkU RWHW[ RZFZW RdUd[ RhWkZ RhSlV RfPaU R`W]Z RaE\\J R_GdLkO RbN_QcS RXDSN RiFdL]O',
	    'PoSE^E RSI]I RfNjN RRUjU RXCXN R_U_[ RcJ`N RdRlV ReWlZ RVQ]T R\\PXR RaRYVRZ RdEcL R`EkEjN R[LTM R^LVM',
	    'Po\\GkG R`MhM RRP[P R^PjP Rc[g[ RWOW[ RgIlN RSHWLSO RaI\\N R[R[W RZL[Q RVCTI RZFXK RcC_K R`P`UiUg[ RTSRY',
	    'Po]GlG RRP[P RXOX[ RdCdG RhJlN RTIVLSP RaK]N RZS\\V RZL\\Q R`OcUgYl[ RVCTI RYFVL RhNfSaX[[ RTSRY',
	    'Po`MiM R_SjS R][l[ RWUWZ ReMe[ ReElL RUHRL RXMRR RdCcG]L R[T[Y RWCTJ RSUR[ RZHXLRL RZN[RRR',
	    'Po]GlG R`PjP R`UjU Rf[j[ RWTWZ R`J`[ R_MjLj[ RWMRQ R`L\\P RYT[X RWCSL RcD^O RSTRZ RYNZRRR RYHXLRL',
	    'PoRP[P R_ZjZ RWOW[ R_R_[ R_RjRj[ RSHVK RVLSP RZR[W RYL[Q RaHgOlQ RVCTI RYFVL RSRRY RbC`H\\K RaGjG RhGeMbO^Q',
	    'PoRP[P R_QfQ R_VfV Re[j[ RXOX[ R_L_W R_LfLfU RSHWLSP RZR\\V RZL\\Q RVCTI RZFWL RaC]L RTRSY R_HlHlSj[',
	    'Po]IlI RRP[P R]QlQ RXOX[ RhIh[ RSHVL RWLSP RZR\\W RYL[Q R`DaH RVCTI RYFWL RiCgH RTRSY RaIaQaW][',
	    'Po^ElE RgUlU R^ZlZ RWTWZ R^K^[ RhEhU R^KlKl[ RUHRK RWLRQ RZT[X RWCTJ RSUS[ RZM[RRR RcEcOaU RZHXLRL',
	    'PoaLkL RRP[P RWOW[ R_H_[ RSHVLTO RZRZV RYL[Q RhSk[ RiEkI ReRbZ RVCTI RYFWL RaC]L RTSRY RfCfNdX RfIhV',
	    'Po_LhL RRP[P RWOW[ RdElL RdE[L RZR[W RYL[Q RVCSHWKSP RYFWK RSRRY R_QjQj[ R_Q_[ R_Z RiZ',
	    'PoaIiI RRP[P RbThT Rh[l[ RXOX[ R^E^[ RbNbV RbNhNhT R^ElEl[ RSHWLSO RZS[W RYL[Q RWCTI RZFXK RTSSY',
	    'Po\\HlH RRP[P RWOW[ RRHVLSO RZRZW RZL[Q RgDkF RVCTI RYFVL RTRSY R`K`Q_W RdQ\\R ReDeMgYl\\lX RjMgUa[',
	    'PoRP[P Rf[l[ RWOW[ RgCgZ RlXl[ RhPlT R`W\\[ RSHWKVLSO RZRZW RYL[Q R]G_L RkGhL RVCTI RYFWK RaQ\\S RTSRY RbCbQaW',
	    'Po`LjL RRP\\P R^YlY RWOW[ R^E^[ R^ElEl[ RYIVL RSHWLSO R[S[W RZL\\Q ReGeOiV RWCTI RZFXJ RbV`V RTSSY ReJdS',
	    'PoVGgG RSL^L RVQaQ R^UjU RZUfU RTUcU R[[_[ R_U_[ R[J^L RhSlV RaLjKkM RdIaL RWHSL ReWlZ RbDhG R]NVQ RYWRZ R]CVG RcPSU',
	    'Po^LlL R_ZjZ RWTWZ R_R_[ ReEeR R_RjRk[ RVHRK RZHXLRQ R[T[X RWCUJ RSTRZ RjD^F RXKRL RZN[QRR',
	    'PoUHiH RSKkK RWQ`Q R]UhU RVUcU R[[_[ R_U_[ RXDYG RfSjV RfEcH RcKfOlQ RcWjZ R_C\\JWORQ R]M[OWQ RdOUV RYXTZ RbP[Q',
	    'PobMkM RSUjU R_U_[ RbEbM RbEkEkM R\\NYQ RUGTJ]N ReRkT ReWlZ RWP]R RXCUH RRF^F\\IYLSO RcO`QYU RZWRZ',
	    'Po]HlH R^OfO RWTWZ RfVk[ RVHRK RZHXLRQ RZT[X RdDeF RXCUI RbJ`O RcHaM RhLbS]T RkPdW\\[ RSTRZ RXKRL RZN[QRR',
	    'Po]ElE R\\MkM RRP[P R]SkS R\\ZlZ RXOX[ RdOdZ RgIlN RVMSP RZR\\W RSHWKVM RYL[Q RVCTI RYFWK RcE_M RTRSY',
	    'Po\\GkG R]NjN RRP[P Rf[l[ RXOX[ RdCdG RgPg[ RlXl[ RgJkN RSHVLSP RZS\\V RZL[Q RVCTI RYFWL RbG_N RTSRX RaQ`X][',
	    'PoRP]P R_PkP RYPY[ RfPf[ R`HdL RhLkO RfJaP R[FYIWL RSHWLTP R\\S]W R[L]Q RiSlY RWDUH RTSSY RdCbH RhGgJ RbS_Y',
	    'PoRP[P R^QkQ R^WkW RWOW[ R^S^X ReNe[ RSHVLSO RaE]J RZS[W RYL[Q R`GdKkN RVCTI RWJVM RYGXK RaFiFdL]O RTSRY',
	    'Po]IlI R^OkO R]UlU RWTWZ RdId[ RUHRK RYHXLRQ RZTZX R_CaG RWCUI RiCgH RSURZ RXKRL RYN[RRR',
	    'Po_LiL RRP[P R]QkQ RWOW[ RhUk[ RSHVL RdClK RWLSP RdC\\K R[R[W RYL[Q RVCTI RYFWL RSRRY RbQ_ZjY',
	    'PoRP[P R_RkR R^[k[ RXOX[ R^L^[ ReLeR R^LkLkR RlWk[ RZR\\W RSHWKSP RYL[Q RVCTI RYFWK RaC]L RaFgFeL RTRSY',
	    'Po^FhF RRF\\F RTNWN RVR_R R\\UhU RYUeU RUUcU RZ[_[ RXCXN R_U_[ RiNlMlN ReRjW RbJ^N R_IeL RdWkZ R\\OVQ R^RTU RcOXT RYXR[ RhGiN RcCaK R\\IRJ',
	    'Po_IjI R_NjN RRO\\O R_SjS Rf[l[ RXOX[ R_D_R RgSg[ R_DjDjR RlXl[ RaW][ RZFYJVL RSHWKSO R[S\\W R[L\\Q RVCTI RTRSY RbS`Y',
	    'Po`JkJ RRP\\P R_RlR R_VlV Rh[l[ RXOX[ R_M_[ R`D`J R`DkDkJ R_MlMl[ RSHWKSP R[S]W RZL\\Q RWCTI RZFXK RTRSY',
	    'Po\\MlM RRP[P RhYkY RWOW[ R`P`Y ReNe[ R^RkQkX RRHVLSO RcM[S[W RZL[Q R_DkJ RVCTI RZFXK RcJaP RiE]J RTSRY',
	    'PoRP[P R^RkR Re[k[ RWOW[ R^I^R ReRe[ R]KkKkR RlXk[ RSHVLSO R_H[L RZR[W RYL[Q RVCTI RYFWK RaC^J RbV_Y[[ RTSRX R`FgFdKcV',
	    'PojRjY RaIcM RZHXLRR RZT\\Y RWCSL RlIbM RhO_S RiS_X Ri[e[ RWUWZ RSUS[ RfGhK^N RjD]F RXKRL RiOjW RZN[QRR',
	    'Po`LkL RRP\\P R_ZlZ RXOX[ R_Q_[ R`D`L R`DkDkL R_QlQl[ RSHWLSP R[S\\W RZL\\Q RVCTI RZFYJ RTRSY ReLdR',
	    'Po^HlH RWUWZ R^NcNcU RgDjF RhTa[ RWMRR RZTZX RkMfV RWCTJ RbV_V RSUS[ RlWk[i[ RYHXLRL RZNZRRR RfCfNgY R^H^S\\[',
	    'Po\\HlH RRP[P R_[c[ RWOW[ RcCc[ RfTlY RjMgP RSHVLSO RZS[V RYL[Q R^L_P RcMhV RgDkF RVCTI RYFXK R`U^V[X RcJfT RTSRY',
	    'Po]GlG R^QkQ RWTWZ R^K^Q RdCd[ R^KkKkQ RfTlY RcS\\Z RUHRK RXLRQ RYTZX RdOfRgV RWCUJ RdObT RSTRZ RZHXLRL RYN[RRR',
	    'Po\\GdG R\\MdM RRPZP R\\RdR RVOV[ RfEf[ RRHVK RYSZW RXLZQ RVDTI RXFUN RTSRY RlWhW RfElEiNlTlV R`C`R_X',
	    'Po\\GkG RRP\\P R^QjQ Rf[l[ RXOX[ R^J^P RdCdF RgQg[ R^JjJjP RlWl[ R`W][ RSHWLSP R[S\\W RWJVM RZL\\Q RVCTI RZFXK RSRRY RaQ`X',
	    'PoRP[P R_PjP R_UjU Rf[j[ RXOX[ R_K_[ RdCdK R_KjKj[ RSHVL RhElI RVMSP R_E^I RZR\\W RZL[Q RVCTI RYFWK RTRSY',
	    'Po`LiL R]QkQ Rg[l[ RWUWZ R]G]K RgQg[ R]GlGlK RlWl[ RcCdF RaV][ RVHRK RZHXLRR RZTZX RXCUI RSURZ RbQ`X RXKRL RZN[RRR',
	    'Po]HlH R_LjL RRP[P R_RjR RXOX[ R]O]R R_DjDjK R]OlOlR RRHVLSO R[R[W RYL[Q RaTeXk[ RVCTI RYFXK RTSRX RiSeX^[\\[',
	    'Po\\ElE R]MkM RRP[P R]RkR RWOW[ R]I]R R]IkIkR RSHVLSO R[R[W RZL[Q RVCTI RZFXK RcU`Y[[ R^TbXeYk[ RTRSY RdEdQcV',
	    'Po]ElE RgQkQ R^VkV R^ZkZ RWUWZ R^J^[ RcEcO RgEgP R^JkJk[ RVNRQ RZT[Y RWCSL RYHVN RSURZ RWKRL RYMZQRR',
	    'Po^MkM RRP[P RWOW[ RdHd[ R]HkHkM RiChG RdPcU]Z RSHWLSO R[R[W RZL\\Q R`DaH RVCTI RZFXK RhYkYhY RTSRY R^M]SlRkY',
	    'Po]IlI RRP[P Rf[j[ RWOW[ R]EdEeO R`W\\[ RSHVLSO RZR[W RWJVM RYL[Q RVCTI RYGWK RTSRY RdHjNlN RdH^PfPfUkTj[ RaQ_X RiDaE',
	    'Po]GkG RWTWZ RfUkZ R`LbQ RUHSL RWMRQ RYTZX RdCdRgW RiLkQ RWCTJ RcU_Y[[ RiJgR RSTRZ R`J^R RdMcV RYHXLRL RYNZRRR',
	    'Po\\MlM RRP[P RaZgZ RWOW[ R\\G\\J RdCdG R\\GlGlJ RSHWLSO R[R[W RZL[Q RiTl[ RVCTI RZFXK RTSRX RePbY RaJ_W RjYdY',
	    'PoRP[P R_ZiZ RWOW[ R_R_[ R_RiRi[ RdIlQ RgDlI RcK]Q RaE]I R[R[W RYL[Q RVCSHWKVLTP RYFWL RSRRY',
	    'Po]FkF RRP[P R]TlT R`[d[ RWOW[ R]J]N RdCdJ RdRd[ R]JlJlN R_NiNfQ RSHWLSO R[R[W RZL\\Q RVCTI RZFXK RTSRY',
	    'PocMkM RRPZP RcVkV RcZlZ RVOV[ RgEgV RSHVK R^VbZ RYSZW RYLYQ RVCTI RXFUN R]Q^W RTSRY RjDbG R\\E`E]NaN_X',
	    'PoRP[P R]PlP RWOW[ RdGdK RSHVLSO R[R[W RYL[Q RiFlL R`UkZ RVCTI RZFXK R_E\\L RcL_V RTSRY RiPgVdXaZ\\[ RiD_E',
	    'Po_QkQ Rf[l[ RWTWZ R_I_Q RgQg[ R_IkIkQ RlXl[ RaW][ RUHRK RZHXLRQ R[T[X R`CbG RWCUJ RiCgI RSTRZ RbQ`Y RXKRL RZN[QRR',
	    'Po`KjK RRP\\P R^YlY RWOW[ R^D^[ ReGeW R^DlDl[ RSHWLSO R[R[W RZL\\Q ReKjT RWCTI RZGXK ReK`U RTRSY',
	    'Po]SlS Ra[d[ ReQe[ R^MjMfP RVHRK RZHXLRR RZT[X R_GaK RcGeK RkFhK RXCUI RWUXZ RTURZ RjC_E RXKRL RZNZRRR',
	    'Po]ElE RRP[P R^SkS R\\[l[ RWOW[ RdSdZ R^L`Q RgLjO ReGcL RRHWLSO R_G]L RjGhL R[S[W RXJWM RZL\\Q RdMeQ RVCTI RZFXK RTSRY',
	    'PoRG]G RUL[L RRP]P RdQkQ Rb[e[ RW[[[ RaQgQf[ RXF]K RhI`Q RWGRL RcH`K RiTlZ RiMkR RXCUJ RdEbJ RaT_Z RVPUT\\T[[ RjC`E RfK`L',
	    'Po]HlH R^NkN Ra[h[ RWUXZ RbRbZ RdCdN RhWh[ RWMRR RZT[Y RdPfT RjSlY RTISL RWCTJ RSUR[ R^S]Y RYHXLRL RZN[RRR',
	    'PogMkM RRPZP RcVlV Rb[l[ RWOW[ RcJcV RgEgV RRHVL R]VaZ RZRZW RXLZQ R\\S_Y RVCTI RYFVM R_R\\Z RTSRY RkDbF R[F_E\\N`M^W',
	    'PogLlL RRP[P RaVhV Rh[l[ RXOX[ R]E][ RaOaX RgEgL RaOhOhV R]ElEl[ RXIWL RSHWLSO RbJ_M RZS[W RYL[Q RWCTI RZFXK RTSSY RbEbL',
	    'PoRP[P R^PjP R_TjT R]WlW RWOW[ RdNd[ RXIVL RRHVLSO RbD]J RZS[W RYL[Q R`GeLlO RVCTI RYFXK RaFiFdL]O RTSRY',
	    'Po_MlM RRP[P R]ZlZ RWOW[ R]D][ ReDeX ReOkT RRHVLSO RZSZW RYL[Q R`FaK RjFhL RVCTI RYFWL RTSRY RfNbS_V',
	    'Po]FlF R^JjJ RRP[P Rg[l[ RWOW[ RdCdJ RgQg[ R]NkNkQ RlXl[ RRHVKSO R[R[W RYL[Q RVCTI RZFVK RTSRX R]N]Q RaRaW\\[',
	    'PoeOiO RRP[P Ra[h[ RWOW[ RbTb[ RhXh[ RbK^O RRHWKVLSO RZS[W RYL[Q RiTlY R^HfK RaQfS RVCTI RYFXK R^U\\Z RTSRY RcEaM R]EkEjJiO',
	    'Po`KiK RRP[P R^PlP Ra[d[ RXOX[ R]F]J RdCdG RdPd[ R]FlFlJ RSHVLSP RZS\\V RZL\\Q RiSkY RVCTI RYFVL R_S]Y RTSRY',
	    'Po[JbJ RdJkJ RRPZP RVPV[ R_C_[ RgCg[ RRHVL R_NcQ RVMSP RYRYW RYLZQ RfMbV RVCTI RYFVL RgIkU RSSRY R_I\\T RgIdT',
	    'Po\\OlO RRO[O R]TkT R\\ZkZ RXOX[ R_J_T R]EdEdZ R]JjJiT RRHVLSO RZS[W RYL[Q RVCTI RYFXK RTSRY RjDbE',
	    'Po]EkE R^NkN RRP[P R_SkS RWOW[ R^IkIkN RfVk[ RdV`Z RSHVLSO R[R[W RYL[Q ReRhY RVCTI RWJVL RYFXK RTSRY ReQcX R^I^R]Y',
	    'Po_JhJ R\\MlM RRP[P Ra[d[ RWOW[ RdMd[ R^PaS RjPfT RSHVLSO R[R[W RYL[Q RVCTI RZFWL RbV\\Y RTSRX R`C_K R`FiFgM RdOgUlZ',
	    'Po]GlG R^QjQ R`[d[ RVTWZ R^J^Q RdQd[ R^JjJjQ RUHRK RWMRR RYT[X RiTkY RcCeG RWCTJ R_T^X RSUS[ RZHXLRL RYN[RRR',
	    'Po`IiI R`MiM RRP[P RaWiW Rh[l[ RWOW[ RaQaX ReFeO RaQiQiW R^ElEl[ RSHWLSO R[R[W RZL\\Q RWCTI RZFXK RTSRY R^E^S]Z',
	    'Po_IiI R\\MlM RRP[P ReWhW R`[j[ RWOW[ R`R`[ RhNlS RhEkH R`O\\S RjXj[ RRHVLSO RZS[W RYL[Q ReIiQ RVCTI RYFWK R_D]I RbH^R RTSRY RcC`O R`RhRhW',
	    'Po[I_I RUN_N RUSaS R[[_[ RUJUN RUJ`J`N R`V`[ RdDdO RTWZVaViUkX RhHjK RdDjDhH RXETI ReSiU RSD`D_I R\\PUS RYXR[ ReXlZ RjMgM ReQTV',
	    'Po]IjI R]NjN RRO[O R\\QlQ RWOW[ R]E]N RdEdN RgQg[ R]EjEjN R`W\\[ RSHVLSO RZR[W RYL[Q RWCTI RYFWK RTSRY RaQ_Y',
	    'PoSFkF RXHeH RXKeK RRMlM R_U_[ R]OZR RfCeNgPlR RUVfTiV RXR]T RdXjZ RXCYMVQRR RbP_SZU RZXTZ',
	    'Po\\FlF R_NjN RRP[P R]VlV R`[d[ RWOW[ R_I_N RdTd[ RdCdF R_IjIjN RRHWLSO R[R[W RZL\\Q RVCTI RZFXK R^QjQdS RTSRY',
	    'Po]GkG R`LhL R`PhP R]UlU RVTWZ R`C`T RhChT RgVl[ RaV\\[ RWMRQ RYTZX RWCTJ RSTRZ RYHXLRL RYNZRRR',
	    'PoRP[P RhUkU Rf[l[ RWOW[ R]G]J RdCdG RgLg[ RfLkLkT R]GkGkJ RlXl[ R^PbT RaV\\[ RRHWKVLSO RZSZW RYL[Q RWCTI RYFWK RcP_Y RTSSY R_J]S R_MdMaW',
	    'Po^FkF R_IjI R]LlL RRP[P R_TjT Rf[j[ RWOW[ R_O_[ RdCdL RdOdT R_OjOj[ RSHWLSO R[R[W RZL\\Q RVCTI RZFXJ RTSRX',
	    'PobGkG RRP[P R]ShS RWOW[ RlXl[ RSHVLSO R[R[W RZL[Q RaDcKfMjN RaPdWfZj[ RkLkN RVCTI RYGWK RdOhP ReDiE RTRSY R]HcHiHdL]M RiTgWbY[[ RkRgR',
	    'PoRP[P RWOW[ R]L]O R]LkLkO RSHVLSO RZS[W RYL[Q R^G_J RcFcJ RaQbTeXl[ RjFhL RVCTI RYFWL R^QiPhTdX\\[ RTSRY RjD^F',
	    'Po^JkJ R_NjN RRP[P R_TkT R_ZlZ RWOW[ R_H_[ RfHfZ RSHVLSO RZS[W RZL[Q RVCTI RYFWL RaC]L RgDeK RTSRY',
	    'PoSE^E RbGlG RTL]L RSUiU R_U_[ RaO_R R\\MXQ RbHfMlO RcRkU RdWkZ RVP\\S RcC`I RiHeL_N R^SXU RZWRZ RTG]G]K RTGSO',
	    'Po`LhL RRP[P R^PiP R_WhW Rf[j[ RWOW[ R_R_X RcLcW RhRhX RRHVLSO RZR[W RYL[Q RWCTI RZFXK R`C\\L RaI_N RTSRY R_HlHlTj[',
	    'PoRP[P R_PjP R_ZkZ RWOW[ R]G]J R_J_[ RdCdG R_JjJjP R_TkTk[ R]FlGlJ RSHWLSO R[R[W RZL\\Q RWCTI RZFXJ RTSRX',
	    'Po`LjL RRP[P RaViV Rg[l[ RWOW[ R^E^[ RaOaX ReLeV RiOiX R^ElEl[ RSHWLSO RZR[W RZL[Q RbGbK RWCTI RZFWL RhGgK RTSRY',
	    'Po`KjK RRP\\P R`PjP RbWiW Rh[l[ RWOW[ R^E^[ RbPbW ReKeP R^ElEl[ RZFWK RSHWKSO R[R[W RZL\\Q RaGbJ RWCTI RTSRY RhGgJ',
	    'PoRP[P R`[c[ RWOW[ R^DcDcZ RfDlDl[ R`PaR R_IaK RhPjR RhIjK RSHVLTO R[R[V RWJVM RYL[Q RVCTI RYFWL RSRRY Ri[l[i[ R^D^R\\Y RfDfTe[',
	    'PoRPZP RWOW[ R^TaX RRHVKTO RZRZW RYL[Q R]HcK ReHkK RdEkEfM RVCTI RYFVL R\\EbE^L R\\QbQ][ RcQkPiVfYb[ RTSRY ReRfVl[',
	    'PoRP[P R]PkP RWOW[ RcGdK RdMd[ RfRlX RePgT RbR\\Y RSHVLSO RZS[W RYL[Q RiFkL RVCTI RYFXK R_F\\L RTSRY RiD_E',
	    'Po^IjI R^NjN RRP[P R\\RkR RWOW[ R^E^N RdEd[ R^EjEjN ReSlY RcS\\Y RXIVM RSHVLSO RZS[W RYL[Q RVCTI RZFXK RTSRY',
	    'Po]GlG RRP[P R\\TlT RWOW[ RdCdG RdRd[ R`LcO RgLlP RSHWLSO R[S[W RZL\\Q RhIeO RWCTI RZFXJ RaI^Q RTSRY',
	    'Po_KiK RRP[P R^UkU Rh[k[ RWOW[ R^O^[ RbOb[ RfOf[ R^OkOk[ RdClK RRHVLSO RdD\\K RZS[W RYL[Q RVCTI RYFVL RTSRY',
	    'PogIkI RRP[P R_ZjZ RWOW[ R_R_[ RgDgN R_RjRj[ RbL\\Q RRHVLTO RZRZV RYL[P RVCTI RYFWL R_C]J R_GdGaN RTSRY R^G`LcOkP',
	    'Po^FlF RRP[P R_WeW Re[j[ RWOW[ R_Q_Y R_QeQeW R]NkNj[ RRHWLSO R[R[W RZL\\Q RdIkL RVCTI RZFWL RTSRX ReCdIbK^L',
	    'Po]LjL RRP[P RdRjR RWOW[ R\\G\\J RdCdG RdLdZ R\\GkGlJ RSHWLSO RZR[W RYL[Q RVCTI RYFWL RTSRY R_P][ R^UbZm[',
	    'Po^KkK R^PkP R]UlU RSURZ RWTWZ R^F^P RgQg[ R^FkFkP RdO_T RUHRK RZHXLRQ RZTZX RWCUI RcCdFeMbR ReHdP RXKRL RZN[RRR',
	    'PoeFlF R_NkN RRP\\P R_RkR R]VlV RWOW[ R_J_R ReCeJ ReRe[ R_JkJkR RZR\\W RSHWKSP RZL\\Q RVCTI RYFWL RTRSY',
	    'Po^FjF R\\JkJ RRP[P RWOW[ RdCdJ RgJfOkOlN RbP]U RRHVLSO RZR[W RYL[Q R`SeXl[ RVCTI RYFVL RaJ`N\\O R`SiSeX][ RTSRY',
	    'Po_JkJ R_NkN RRP\\P RiYlY RXOX[ R_Q_Y R_F_N ReNe[ R_FkFlN R_QlQlX RZGWL RSHWLSP ReCdF R[S]W RZL\\Q RVCTI RTRSY',
	    'Po\\FkF R[LlL RRP[P R^PjP R\\SlS RWOW[ R^IjIjP RRHVLSO RZR[W RYL[Q RVCTI RYGWK RdCcP_Wk[ RTSRY RhUdY[[',
	    'Po\\FkF R]MlM R]QiQ RcUjU RcZlZ RWUWZ RdCdZ R]JjJjQ R^UcZ RVNRR RZTZY RVCRL R_T\\[ RTUS[ RYNZRRR RYHWLRL',
	    'PoRP[P RXOX[ RgKkO RjEiI RSHVLTP RjQhU RZR\\W RYL[Q RdSfWhYl[ RcIhK RVCTI RYFWL R_E^I R_Q^U RcJ`M]N RcU`Y\\[ RTRSY RdCcK RdPeV',
	    'PoRP[P RdSkS Rc[l[ RWOW[ ReNe[ R`WcZ R`HcK RgGlL RSHVLSO RZS[W RWJVM RZL[Q R_TaY RVCTI RYFWK RaD]L RhEdL RTSRY R_P]Y',
	    'Po^IjI R^MjM RRP\\P R_SdS Rf[l[ RXOX[ R^D^M R_O_Z RfOf[ R^DjDjM RlXl[ RSHWLSP R[S\\W RXJWL RZL\\Q RVCTI RZFXK RkRfT RTSRY RdY]Z',
	    'Po[ElE R]KbK RRPZP R]QbQ RVOV[ R]E]W RbEb[ RRHVK RYRZV RXLZQ RfQjZ RjPfZ RVCTI RXFUN ReLhX RSSRY RdJkJhV RbV[W',
	    'Po_HkH R_MkM RRP\\P Rg[j[ RXOX[ R_D_M R_DkDkM RaO]S RcS]Y RfVb[ RYJVL RSHWLSP R[S]W RZL\\Q RhQeY RVCTI RZFXJ RdQbV RTRSY RbM`RlQk[',
	    'PoRP\\P R^UlU R^ZlZ RWOW[ R^P^[ RePeZ R^PlPl[ RcIeL RhIkL RjEhI RZFXJ RSHWLSO ReEcI R[R[W RZL\\Q R`C^I`M RWCTI RTSRY',
	    'Po^GkG R\\KlK R^QkQ R\\UlU RVTWZ RdCdK RdQd[ RVHRK RZHXLRQ RZTZX RaMaO RhMhP RWCUI RSTRZ RXKRL RZN[RRR',
	    'Po]ElE Rh[k[ R]J][ RdEd[ R]JlJl[ RVHRK RWLRQ RYTZY RWCTJ R`McV RgMjV RhPfV RSTR[ RaO_V RVTWZ RZHXLRL RYNZRRR',
	    'PoSD^D RSK^K RSN^N RRUjU RSDSN RYEYG RYKYN RSG^G^K R_U_[ R\\PXQ RbHgMlO RhTkW RdWkZ RVQ\\T R`EjEiIeL`N RcPYU RZWRZ',
	    'Po]HbH RfHlH R]NbN RfNkN RRP[P RfTlT RWOW[ RfCf[ RSHVLSO R[R[W RZL[Q RVCTI RZFWL RTRSY RbCbQaW][ R^TaT',
	    'Po]IlI RRP[P R\\RlR RWOW[ ReEeO RSHVLSO R`L\\N RdH_M R[R[W RYL[Q RdHhLlN RcWk[ RVCTI RZFXK RcO_WjY RTSRY RhRfX\\[ RiD`E',
	    'Po^IlI RRP\\P R_VjV R_ZjZ RXOX[ R^E^O R_Q_[ R_QjQj[ RSHWLSP R[S\\W RWJVM RZL\\Q RVCTI RZGXK RSRRY RlMkPiO ReDgM ReN]O RiC^E',
	    'Po\\HlH RRP[P R]RcR RWOW[ RZRZV R]L]R R]LcLcR RlWk[ RiCkF RgT`[ RSHVLUO RYL[Q RkLfV RVCTI RYFWL RTSRY RcU\\W RfCfMhY',
	    'Po`NjN RRP\\P Rb[h[ RWOW[ RbTb[ RhXh[ RhLkO RgDlI RSHVLSO RZS\\W RZM\\Q RiTlZ RbQeR RVCTI RYGWK RaC^I RdI`N R^U\\Z RTSRY',
	    'PoUF_F RSP_P RaQkQ RY[\\[ RUJ^J^X RfQf[ RXKZM RWRZT RiNlQ RaIfNbQ RTW^W]Z RiTlY RhIfN RcT`Z RVCSJ RfDcJ RUIUNTQTX RjD`E',
	    'Po_IiI R\\MlM RRP[P Ra[d[ RWOW[ RdMd[ R^DiDiM R^PaS RkPhS RRHVLSO R[R[W RYL[Q RVCTI RZFVL RbV[Y RTSRY RdPgUkY',
	    'Po]GhG R\\LlL RRP[P R`UjU R`ZjZ RWOW[ R`P`[ RcCcL R_QjQj[ RSHWKSO R[S[W RZL\\Q RWCTI RZFWK RjDiHcNaP\\R RTSSY',
	    'Po]FkF R]SlS RWTWZ R_L_R RaCaI RgCgI R_LiLiR ReUjZ RcV^Z RWMRQ RZT[Y RdIdSgX RUHSL RWCTJ RSURZ RdNcV RYHXLRL RZM[QRR',
	    'PobVhV Rg[k[ RWTWZ R]L][ RbPbX RdCdL RbPhPhV R]LlLl[ R^F`I RkEiI RUHRK RWLRQ RYT[X RWCTJ RSURZ RZHXLRL RZN[RRR',
	    'Po\\FkF RRP[P R_PiP R_UiU RWOW[ RdCdF RiNi[ RXIWL RSHWLSO RZR[W RZL[Q RVCTI RYFXK RiFdLlN RTRSY R_GeK^N_U^Y',
	    'Po]GhG R\\LlL RRP[P R`UjU R`ZjZ RWOW[ R`P`[ RcCcL R_QjQj[ RgNjO RSHWKSO R[R[W RZL\\Q RWCTI RZFWK RjDiHbNaP\\R RTSRY',
	    'Po[JcJ ReKlK RRPZP ReRlR ReYlY RWPW[ R`C`[ ReDe[ ReDlDl[ R`McQ RRHVLSP RYRZW RYM[Q RVCTI RYGWL RTSRY R`H]S',
	    'Po]HkH R_KeK RRP[P R_UdU RWOW[ R_O_W R_OdOdU RhDlE RgUb[ RRHVLTO RZRZV RYL[Q Rj[k[ RkMfW RlWk[ RVCTI RYFVM RTSRX RgCgLgOiZ R]H]T[Z',
	    'Po]GlG RaJhJ RRP[P R_SjS R]VlV RWOW[ R_N_S RaDaJ ReJe[ RiDiJ R_NjNjS RXIWL RSHWLSO R[R[W RZL[Q RVCTI RZFXJ RTSRY',
	    'Po^JjJ R^NjN RRP[P R`[d[ RWOW[ R^F^N RdNd[ R^FjFkN RjQgT RSHWLSO RcCbF RZS[W RYL[Q RVCTI RYFWL RTSRY R]SbS`W\\Z RdPgUkZ',
	    'Po]ElE RhQkQ R\\TlT RWTWZ R]I]Q RcGcQ RfIfP R]IkIkQ RWMRQ RZT[Y RdSeWgYl[ RTHSL RWCTJ RcV`Y\\[ RSTRZ RcRbX RYHWLRL RcEbL RYM[QRR',
	    'PoRJ\\J RRN\\N R_RkR RZY]Y RRQRY RRFRN RWNW[ RRF\\F\\N RRQ]Q]X RfQf[ R`IdN RhOlR RiIfLaR RVCVF ReDcHaJ RiUkY RaU_Z RjC_E',
	    'Po_IjI R\\LlL RRP[P R_PiP R_TiT ReXlX RbXiX R^YfY R\\YdY RWOW[ R_D_I R_L_X RiLi[ R_DjDjI RSHWLSO R[R[W RZL\\Q RVCTI RZFXJ RTSRY',
	    'Po]JaJ RRPZP R]PaP RVOV[ R]E][ RdDdK RdDjDjMlLlM RfVk[ RRHVK RYRZW RXLZQ RdPgX RVDTI RXFUN RcOkOhVdYa[ RbD\\F RSSRY RbT[U',
	    'Po]HlH R^OkO R^SkS R^WkW R\\[l[ RWUWZ R^K^S R^EeEe[ R^KkKkS RVHRK RWMRR RYT[Y RWCUJ RSUR[ RjDbE RZHXLRL RZN[RRR',
	    'Po]FlF RRP[P RgXjX RXOX[ R]K]O R_P_X RdCdF RdMd[ R_PjPjX R]KlKlO RSHWKSO RZS[W RYL[Q R`FaK RVCTI RZFWK RiFfK RTSSY',
	    'Po^HjH R^LlL RRP[P R^VjV R^ZjZ RWOW[ R^D^P R^R^[ R^DjDjH R^RjRj[ RSHVLSO RZR[W RYL[Q ReIgOkPlQ RVCTI RYFXK RTSRY RcO]P',
	    'Po]GlG RRP\\P R^TkT R^ZkZ RXOX[ R^M^[ RaCaK ReMeZ RhChK R^MkMl[ RSHWLSP R[S\\W RZL\\Q RVCTI RZFXJ RTRSY',
	    'Po_IhI R\\LkL RRP[P RWOW[ RSHVL RSO RkOgS RZR[W RYL[Q RVCTI RYFVL RcLaO]Q RcR]T RTSRY R_C_J R`FhFgL RbOeUd[a[ ReRiVmY ReUbW[Z',
	    'Po\\GlG RdKiK R_OkO RRP[P R\\SkS RWOW[ R_D_O RdCdK RdOd[ RiCiK RdRkY RSHWLSO RdR]Y R[R[W RZL[Q RVCTI RZFXK RTSRY',
	    'PobLkL RRP[P R`PlP RWOW[ R_G_[ RbEbL RfLf[ RbEkEkL RiUlX RdT`X RRHVLTO RZS[V RYL[Q RfOjV RfOcV RVCTI RYFWL R`D\\L RTSRY',
	    'Po^JjJ R^OjO RRP[P Ra[h[ RWOW[ R^D^O RbSb[ RdDdO RhXh[ R^DjDjO RSHVLSO RZS[W RYL[Q RiTlY RaQfS RVCTI RYFXK RTSRY R^T\\Y',
	    'Po\\HlH RfRkR R]VkV R]ZkZ RVTWY R]M][ RbHbQ RgGgR R]MkMk[ RhDfI RWMRQ RYTZX R`CaF RWCSL RSTRZ RYNZRRR RYHXLRL',
	    'Po]ElE R^MkM RRP[P R_UlU RWOW[ RcQc[ RhQh[ R^HkHkM RSHWLSO RZR[W RYL[Q RWCTI RZFWL RTSRY R_QlQl[i[ R^H^O]X R_Q_[',
	    'Po]KkK R\\OlO RRP[P R`RiR RWOW[ RjFhJ RSHVLSO RZRZW RZL[Q R^G_J RcFcJ R`SdXl[ RWCTI RYFVL RiTeY_[ RTRSY RjDdE^F RaK`S^X[[',
	    'PobElE RRPZP Ra[l[ RVOV[ R_C_[ RRHVK RcQfT RdNhO RYSZW RXLZQ RaHaL RVCTI RXFUN RTSRY R\\H[N ReFbT RdKkKh[',
	    'PoRP[P Ra[h[ RWOW[ RaTa[ RhXh[ ReLaP RRHVLSO R`E[J RbI]N RZSZW RZL[Q RiTkZ R_JgM RaRfT RVCTI RYFWK RcGaK R^U\\Z RgGdN RTSRY RiPfP R_GkGjP',
	    'Po]ElE RRP[P RbPgP RbUgU R^ZlZ RWOW[ R^J^[ RbJbZ RgJgZ R^JlJl[ RSHWLSO R[S[W RZL\\Q RWCTI RZFXJ RTSRY RdEbN',
	    'Po^HlH R^LlL RRP]P R_SkS R_WkW Rg[k[ RYOY[ R^D^L R_O_[ ReDeL R_OkOk[ R^DlDlL RZFYJ RSHWKSO R[S\\W R[M]Q RWCTI RTSRY',
	    'Po_GlG R_MjM R_PjP RWTWY R_I_P R_IjIjP RUHRK RYHXLRQ RcP\\V RZTZX R`TdXk[ RWCUJ R`C^I RSURZ R`TjSfXbY\\[ RXKRL RZN[RRR',
	    'Po\\IlI RRP[P R_PjP R]SkS R\\XlX RWOW[ R_L_P R_S_W RfPf[ R_LjLjP RSHWKSO R[S[V RZL[Q RVCTI RYGWK R^EiEiJ RTSRY RbCbJ',
	    'Po_JhJ R]RcR R]VcV R`[c[ Rg[k[ RVTWZ R]N][ R]NcNc[ RfNfW RkMk[ RVNRQ RYTYX RdDgGlI RWCSL RYHVN RdC`H[J RSURZ RXKRL RYNZRRR',
	    'Po`JlJ RbOkO RRP[P R`SlS RWOW[ R^H^[ RbEiEiJ RgWk[ ReVa[ RSHVLTO R^I[L RZSZV RYL[Q RfNfTiY RVCTI RYFWL R`C]J RcK`R RTSRY',
	    'Po\\JaK RRPZP R\\PaQ RdSgS R\\W`W RVPV[ R\\D\\Y R\\D`D`W RgCgS RRHVLTP RYRZW RYLZQ RVCTI RYFVL RjQiVfY`[ RjGlN RSSRY RcGbO',
	    'PoUFlF RWIaI RXL`L RWN`N RYSbS RaVjV R^[a[ R\\G\\P RYVbVa[ R_CaE RfXlZ R`PYS R\\XV[ ReHdN RjOfO RdSWV RUFURSZ RbHjIjO',
	    'Po\\FkF R]NjN RRP[P R]SjS RWOW[ R]J]S RdCd[ R]JjJjS RdRkY RdS]Y RSHWLSO RZR[W RYL[Q RVCTI RYFWL RTSRY',
	    'Po^LjL RRP[P RfPlP RWOW[ R^D^L RgLgP RjDjL RbP]V RRHVLSO RgDaJ RZR[W RXIVM RZL[Q R`TdXk[ RaEfH RVCTI RYFWK RaL_P\\Q RTSRX R`SiSeWaY[[',
	    'Po`EkE RaMkM RRP[P RaQkQ RWOW[ R_H_[ RaIaQ RaIkIkQ ReWa[ RRHVLTO RZRZV R^IZL[Q RbUeYk[ RVCTI RYFWL R`D]K RTSRY RfEfReX',
	    'Po^IjI R_PkP R_TkT R]WlW RVTWZ R]E]H R_L_T RdGd[ R_LkLkT R]DkEkH RUHRK RXLRQ RZTZX RWCUI RSTR[ RZHXLRL RZN[RRR',
	    'Po]ElE R]PkP R^UkU R\\[l[ RWUWZ R]I]P RbEbP RdQd[ RfEfP R]IkIkP RWLVORR RZT[Y RWCTK RSUR[ RYHWMRL RZN[RRR',
	    'Po^HjH R^MjM RRP[P R]OlO RdTkT Rd[l[ RWOW[ R^D^L ReOe[ R^DjDjL RSHVLSO RZS[W RWJVM RZL[Q R_UaY RVCTI RYFWK RTSRY R_R]Z',
	    'PoeIjI RRP\\P RbWhW Ri[l[ RWOW[ R^N^[ RbRbY ReIeN RbRhRhW R`EjEjN R`E_NlNl[ RSHWLSO R[R[W RZL\\Q RWCTI RZFXJ RTSRY',
	    'Po[EcE RZMcM RRPZP R[RcR RVOV[ R_N_X RSHVK RYRZW RXNZQ RaIcN RVCSH RXFTP RSRRY R^E\\M RfCdO RcXZY ReKgTjZ ReJmI RlIhUd[',
	    'Po^IiI R^NiN R[[l[ RWUWZ R]R][ R^E^N RbRb[ RfRf[ R^EiEiN R]RkRk[ RWNRR RZT[Y RUFSL RWCTJ RSUR[ RZGWLRL RZN[RRR',
	    'PodGlG R^OjO R^RjR R^VjV RWTWZ R^K^V RdCdJ R^KjKjV RUHRK RWMRQ RZT[Y RgXlZ RWCTJ RbX\\[ RSTRZ RZGXLRL RZM[QRR',
	    'Po]ElE R`MkM R`QkQ R`UkU R^ZlZ RVTWZ R`H`Q ReHeZ R`HkHkQ RVNRQ RYTYX RWCSL RYHVN RSURZ RYNZRRR R]E]R\\Z RWKRL',
	    'PobIjI R^JfJ R^RjR R^L^R RaVa[hZh[ R^LjLjR RbTeW RgFkJ RUHRK RWMRR RaE]I RYTZY RjUkZ RWCTJ R]V\\Z RSUR[ RVUWZ RZNZRRR RZHXLRL',
	    'Po\\KcK ReLkL RRPZP R\\PbP R\\UcU RVOV[ R\\D\\[ R\\DcDcK ReDkDkK RgVl[ RRHVK RYRZW RXLZQ ReQiY RVCTI RXFUN RdPlPiVeYb[ RSSRY',
	    'Po_HhH R\\LlL RRP[P RWOW[ R^DhDiL RSHVLSO RkOgS RZR[W RYL[Q RVCTI RYFVL RdLaO]Q RcR]T RTSRY RaOeUd[a[ ReUbX\\Z RdQhUmY',
	    'Po_PjP Ra[i[ RWUXZ R_I_P RbTb[ RiXi[ R_IjIjP RcRgU R`DbH RVNRR RZU[Y RiTlY RVCRL RYHWN RiCfI RSUR[ R^U]Z RXKRL RYN[RRR',
	    'PodHiH RcLlL R^SjS R^WjW Rf[j[ RVTWZ R^O^[ RdDdL R^OjOj[ RWMRQ RZTZX R\\ElDkG RWCTJ R_G\\N RSTR[ RYHXLRL RZNZRRR',
	    'Po^HjH R^LjL RRP[P R]SkS R]WkW RXOX[ R]O]Y R^E^L R^EjEjL R]OkOkW RdEd[k[lZ RSHVLSO R[R[W RYL[Q RVCTI RYFVL RTSRY',
	    'Po\\GkG R^KeK RRPZP RVPV[ RRHVL RhDkE R_TcW^[ RgWd[ RUMSP RYRYW RYLZQ RdRaY RjPfY RlWk[ RVCTI RYFVL RkKhW RSRRY RaL^U R^OdObX RgCgLgPhZ R\\G\\TZZ',
	    'PoaElE RbOkO RbVkV RaZlZ RWUWZ R^C^[ RbIbU RbIkIkU RUNRQ RYTYY RVCRL RXHUO R_I`N RSTR[ R[I[O RWKRL RXNYRRR',
	    'Po`KiK R_SjS R_WjW R\\[l[ RWUWZ R]G]J R_N_W R_NjNjW R]GlGlJ RWMRR RdCdG RWCTJ RSUR[ RYHXLRL R[T[Y RZN[RRR',
	    'PoSUjU RSLSO R_U_[ RXH\\J RdXiY RjEhG RUETH R]E[G RbF_H R\\OXQ RdRkV RWQ]S ReHlK RdI_K RcOaQ^RYU RXYT[ RWIRK RYCWI ReCdI RSLkLkP',
	    'Po]ElE RdLkL R\\OlO RRP[P R^VjV R^ZjZ RWOW[ R^R^[ R^RjRj[ RiJlL RRHWKVLSO RZS[W RYL[Q R\\LbKbM RVCTI RYFWK R_G]L RhGeL RTSRY',
	    'Po`IhI RbNgN RRP[P R[[l[ RWOW[ R^S^Z RbSbZ RfSfZ R^SjSjZ RhOlS RcEgElJ RRHVLSO R_G\\J RZSZW RYL[Q RVCTI RYFWK R_M]S RTSRY',
	    'PobMjM RRPZP RbWkW Rb[l[ RWOW[ RbFbX RfCeF RbFjFjL RbPkPkX RRHVL R\\J_L R]D`G R\\P`P_W[[ RZRZW RXLZQ RVCTI RYFVM RTSRY',
	    'PoaHkH RbRjR Rb[l[ RVUVZ RbKbQ RjKjS RcDdG R\\M^M^X[[ RVNRR RXTYX R^Xd[ RVCRL RYHVN R]E^I RSUS[ RXKRL R^M[M RXNYRRR RiDgHfReW',
	    'Po^FlF R^PlP Re[i[ R]K]N RdMeP ReCeF R]KlKlO RaW][ RVHRK RXLRR RZT[X RaHaJ RhGgK RWDUJ RSUR[ RWTWZ RbUjUi[ RbQaX RZHXLRL RZN[RRR',
	    'Po_SjS R_VjV Rf[i[ RWTWZ R\\L\\O R_D_L R_O_[ R_DiDiL R_OjOj[ R\\LlLlO RUHRK RYHWMRQ R[T\\Y RWCTJ R_HeHeL RSTRZ RXKRL RZM[QRR',
	    'PohHlH RRJ]J R`JiJ RVSaS RhTjW RUEUM RUE]E]O RXLZN RXGZI RUVaVgUiU RbJbL RdXkZ RbDaH R[O]PVR RhLaO RYXS[ RbDhDhH RdNlP RdRTV R_V_[\\[',
	    'Po_LhL RgNkN RRP\\P R_XhX Rg[k[ RWOW[ R_H_L R_T_W RhHhL RhThW RaO]T RSHWKSO RaD\\I R[R[W RZL\\Q RWCTI RYGXK RcM]O RcY][ RTSRX RcHcM RcScX R`FlFkN R`RlRk[',
	    'Po\\IlI R\\OlO RRP[P R\\SjS RWOW[ RaEa[ RfEf[ R\\LjLjS RfTlY RaS[Z RSHVLSO RZSZW RWJVM RYL[Q RVCTI RYFWK RTSRY RdD\\E RjDdE',
	    'Po]GlG R^KkK R\\OlO RRP[P RaTkT R_[l[ RWOW[ RdGdO RfTf[ RSHVLSO RZS[W RWJVM RYL[Q R`DaG RVCTI RYFWK R`R\\Z RhCgF RTSRY R`O]Y',
	    'Po]ElE R`MkM R`QkQ RVTWZ R`I`Q R`IkIkQ RWMRQ RYTZX RiTlY RWCSL RYHWN ReFeI RaT_Y RSUR[ ReQe[b[ RYNZRRR R]E]R[[ RXKRL',
	    'Po^EhE R\\MbP R[TkT RVUWZ RcRc[ R\\LbL]Q RdLkLfP RVMRR RZTZX ReNkP RcSgXlZ RVCRL RcS^XZZ R_FiI RSTRZ RhFbH]I RYHWLRL RXNZQRR',
	    'Po^NiN RRP[P R[ZlZ RWOW[ R]Q]Z R^E^N RbQbZ RfQfZ R^EiEiN R]QjQjZ RcHgL RRHVLTO RZS[V RYL[Q RVCTI RYFWL RdFaL RTSRY',
	    'Po^JkJ R^NkN R]QlQ R^ZjZ RWTWZ R]G]J R^T^[ RdHdT R^TjTj[ R]FlFlI RcCfF RWMRQ RZT[Y RUHSL RWCTJ RSUS[ RYHXLRL RZM[QRR',
	    'Po\\FkF R[MlM RRP[P R^QjQ RWOW[ R^J^Q R`T`Z RdCdG R^JjJjQ ReUk[ RWIVM RRHVLSO RZR[W RYL[Q RdQgX RVCTI RYFWK RcQaT\\V RkTfV RTSRY RfY\\[',
	    'PoRP[P R]RkR R\\UlU RWOW[ RhOlR RhFlI RgKaR RRHVLSO RZS[W RWJVM RYL[Q RdFdI RcJ_MdO RdVgYl[ RVCTI RYGWL R_E\\J RdSbWaY\\[ RTSSY RiC_E',
	    'Po\\FlF R]KkK R`OiO R`SiS RdWkW R`XgX R]XdX RWTWZ R`K`X RaCaI RhChI RiKi[ RUHSL RWMRQ R[T[Y RWCTJ RSTRZ RYHXLRL RZM[QRR',
	    'Po^TcT RfTlT R^ZlZ R_O_[ RfNlNl[ RZHXLRQ RVHRK RZT[X R^H`L RdGeK RkGiL RXCUI RdN^O RSTR[ RWTWZ RkD`F RXKRL RZN[RRR',
	    'Po]FlF R^MkM RRP[P R^QkQ R\\TlT Rc[g[ RWOW[ R^I^Q RdCdQ RhQh[ R^IkIkQ R_VbY RRHWLSO RZSZW RYL[Q RgDjD RVCTI RYFWL RTSRY',
	    'Po_HjH R_OkO RRP\\P R_RkR R_VkV RXOX[ R_K_V R_D_H R_DjDjH R_KkKkV RiYl[ RSHWLSO R[S\\W R[L\\Q RVCTI RZGXK RbX\\[ RTSRY',
	    'Po_HkH RaMjM RRP[P RaPjP RaTjT R^VlV RWOW[ R^L^V R_C_H RaJaS RaJjJjS RSHWLSO RZR[W RWJWL RZL[Q RgXl[ RVCTI RZFXJ RaX\\[ RTSRX RiD^E',
	    'Po]FlF R`MjM RRP[P RaXhX Ri[l[ RWOW[ R^P^[ R`I`M RaSaY ReCeF RaShShX R`IjIjM R^PlPl[ RSHWLSO RZR[W RZL[Q RWCTI RYGWL RTSRY',
	    'Po]EkE R`HjH R]KlK RRP[P R_TlT Rd[h[ RWOW[ RaKaQ RhQh[ RaVdX RRHVLSO RZR[W RWJVM RYL[Q ReLiPlQ RVCTI RYFWK RkLgN RTSRY RfP_Q R]E]S\\Z',
	    'PoRE_E RTH]H RiMlM RUN\\N RRV^V RXCXH RXUX[ RSKRL^K_M RbEbK RbEiEiL RlJlM R\\S_V R\\W^Y R[PUU RUWRZ RbQdVhYl[ RWOTQWS RaPjPhUdY_[',
	    'Po^KkK RRP\\PlQ R^UlU RWOW[ R]F]J RdCdG RdHdK RdQd[ R]FlFlJ RSHWLSO R[R[W RZL\\Q RaLaP RhLgQ RVCTI RZFXJ RTSRX',
	    'Po^HiH RTKZK R\\LlL RTRZR R\\UjU RV[Z[ RTDZDZ[ RdUd[ RhWkZ R_QcS RgSkV RgLlQ RhDkH RcN`Q RfPaU R_W\\Z R_E\\H RaL\\Q RfHhN RcF_O RcC`M RTDTSS[',
	    'PoVH]H RVK]K RVO]O RbQkQ RSR^R RSFSR RVDVO RYRY[ RVD]D]O RfQf[ RiNlR RiIfM RaJfMaQ R\\U^X RiTlY RbU`Z RTURZ ReDaJ RjC`E',
	    'Po]FkF R^IjI R\\LlL R^PeP R]TkT Ra[d[ RdCdL RdTd[ RgVkZ RhRlU R`V\\Z RUHRK RWMRQ RYTZX RWCTJ RcM^P RgO]T RSTR[ RVTWZ RYHXLRL RYNZRRR',
	    'Po]FlF R^JkJ R]MlM R]TjT RWTWZ RdPd[ RgVjZ RcThV RfMkR RaM\\R RVHRK RZHXLRQ RZTZX RWCUI RdT`X[Z RSUR[ RcCbM RYNZRRR RgP`Q RXKRL',
	    'PobJlJ RRPZP RgQlQ RWOW[ R_K_[ RSHVKTO R^E[H RZRZW RYL[P RcDdH RVCTI RYFVK R_I]N RjCiH RSRRY RbPaZ RbTdXgZl[ RgJgZ',
	    'PocGlG RRPZP RaSlS RWOW[ R]G][ R_G_V RfOf[ RhUlY RfQdU`Z RRHVLTO RZRZW RXLZQ RfQhV RcHgMlN ReScW RVCTI RYFVM R^C[L RdCaJ RiHeMaO RTSRY',
	    'Po^HkH R^LkL RRP[P R]SlS Rf[l[ RXOX[ R^D^L RdLdS RgSg[ R^DkDkL RlYl[ R^N`Q RiNhQ RSHVLSP R[R[W RWJVM RYL[Q RVCTI RYFWK RaW^Z[[ RTRSY RaS`Y',
	    'Po`OiO R]SkS R`[d[ RdSd[ R]K`L RhVkY R_HbI RhKlO R_W\\Y RVNRQ RbJ\\P RYTYX ReFiM ReFlFiJ RWCSL RYHVN R_C\\I R^FcF`M RSTR[ RVTWZ RYNZRRR RWKRL',
	    'Po^HjH R]PkP R]SkS R\\WlW RVTWZ R^D_L_S RdDd[ R^LiLiS R^DjDjL RUHSL RWMRQ RZTZX RWCTJ RSUR[ RYNZRRR RYHXLRL',
	    'PoRPZP RaOjO RbRjR R`VkV Rb[l[ RWOW[ RfNfY RRHVL R[N_N_XaZ R[D^G R^W[Z RdD_I RZRZW RYLZQ RcFfKlM RVCTI RYFTP RcFjFeK_M RTSRY',
	    'Po\\GlG RRPZP R]PeP RiZk[ RVOV[ RSHVK RiDkF RgVcZ RYRZW RXLZQ RdSdV RjOfX RVCTI RXFUN R\\G]T]X RkLhW RlWk[ RSSRY RaLdLaL RaI`[a[_[ RgCgNgPhZ R\\P[Z',
	    'Po\\FlF R^OkO RRP[P R`WiW Rh[k[ RWOW[ R]R][ R^H^O RdCdF RkHkO R]RlRl[ RgTiX ReK`M RgHcL RRHVLSO RZS[W RWJVM RYL[Q RaIhL RWCTI RZFXK RTSRY RcOaW',
	    'PobJlJ RRP[P RbTkT RbZkZ RWOW[ R]F]J R_M_[ RbOb[ RdCdF RfKfO RbOkOk[ R]FlFlI RSHWLSO RZR[W RYL[P RVCTI RZFWL R`J\\R RTSRY',
	    'Po^JjJ RRP[P R^RjR R^VjV RWOW[ R]F\\I R^N^V RdCdG RdJdV R^NjNkV R]FlFlI RgXkY RSHWLSO RZR[W RZL\\Q RVCTI RYGXK R`Y\\[ RTRSY',
	    'PogGkG R`MlM RfSkS Rf[l[ RVUWZ R^L^[ RcEcM RgNgZ RgCgM R`D[H RVNRR RZT[Y RbTdY RVCRL RSUR[ RbQ`Z RYHWLRL R_H^MZNZRRR',
	    'PoROZO RfSkS RWOW[ R_K_[ RcHeJ RjIkL RSHVKTO R^E[H RZRZW RYLZQ RbTeY RVCTI RYFVL R_I]N RiEfL RTSRY RdDaL RbQa[ RgNfZl[',
	    'Po]HkH R]LkL RRP[P R]UkU RWOW[ R]D]L RdUd[ R]DkDkL RhWkZ RhRlV RcQ`U RgNbS R`W\\Z RSHVLSO RZS[W RYL[Q RdDdL_PcR RVCTI RYFWK RTSRY',
	    'Po]ElE R^KkK RRP[P R]RkR R\\UlU Rd[h[ RWOW[ RdCdQ RhSh[ R^HkHkN R`VcY R^H^NjOlS RSHVLSO RZR[W RYL[Q RVCTI RWJVM RYFXK RTSRY',
	    'Po]FlF R`IfI R`OiO R^TkT R]WlW RWTWZ R]K\\O RdQd[ R\\KlKlO RgMjP RcCeF RgH_P R`R]U RUHRK RWLRQ RcF`I R[T[Y RWCTJ RSTRZ RYHXLRL RZM[QRR',
	    'Po_GjG R_JjJ RRP[P R]QkQ RWOW[ R]M]Q R_D_J R_DjDjJ R]MkMkQ RRHVLSO RZR[W RVCTI RYFWK RZL[Q RbMbQ RfMfQ R_TbXgZl[ RTSRY R]TjTgWbY[[',
	    'PoSE^E RRH_H RRK_K RSM^M RRViV RYCYH RYKYP R_V_[ R`FiFjO RcL`O RVIVK R`JfL RdSkV RdWkZ RVR\\T R[I[K R\\QXR RbQ_SZU RZWR[ RlMkPlO RdCcM',
	    'Po\\HlH R^KjK RRP[P R]QjQ R\\TlT RWOW[ R]M]Q R^E^K R^EjEjK R]MjMjQ RdCdQaT^W RSHVLSO RZRZV RYL[Q RVCTI RYFWL R_Wl[ RSRSY RiTeY[[',
	    'Po]FlF R^MjM R^QjQ R]UlU RWTWZ R^J^Q RaCaH RgCgH R^JjJjQ RVMRQ RZT[Y ReVhYl[ RWCTJ RcV`Y\\[ RSUR[ RdQbX RYHWLRL RYMZQRR',
	    'Po\\EkE R]MkM RRP[P R_PjP R\\TlT Ra[d[ RWOW[ R]H]M RbEbM RdTd[ RfEfM R]HkHkM RhVlZ R_V\\Z RSHVLSO R[R[W RYL[Q RVCTI RZFXK RTSRY',
	    'PoaFkF RaNkN RRPZP RaQkQ R`UlU Rb[l[ RWOW[ RaJaQ RfCfY RaJkJkQ R_P_XaZ R\\J^L R\\D_F R^W[[ RZRZW RYLZQ RVCSIVKTO RYFVM RTSRY R^PZP',
	    'PoTFlF RWI`I RbIkI R[G[O R^K^O R`C`F RaVa[ RfGfO RVVjUkW RaT\\V RbS_T RdQaS R_P\\S RfTjU RZR_T ReXlZ RbKbO R\\XVZ RXKWO RiKiOlNlO RTFTSS[',
	    'PodHlH R[HbH RgRkR RVUWY R]D`F RVNRQ RYTYX RdTfZ RVCRL RYHVN RSTRZ R`Z][ RhLgZl[ RXKRL RXNYRRR RdCcKkLkP R]H]P[Y R]MaMaU`[ RdPb[',
	    'PoRP[P R^PjP RWOW[ R^F^P RaTa[hZh[ R^FjFjP RRHVLSO RcCbF RZR[W RYL[Q RiUlZ RdH`KhM RaRfT RVCTI RWIVM RYFWK R^U\\Z RcIhIdM`O RTSRY',
	    'Po]EkE R^HjH R\\JlJ RRP[P R^PjP R^TjT R^WjW RXOX[ R^M^W RdCdJ R^MjMjW RSHWLSO R[R[W RZL[Q RgYl[ RVCTI RZFWL RaY\\[ RTRSY',
	    'Po]HjH R\\NbN RfNlN RRP[P RWOW[ RjLlN RgEjI RSHVLTO RZRZV RYL[P RdKhPlQ RhJgN RVCTI RYFWL R^J\\N RaC`H RcK_P[Q RdQ^T RgS^W RTSRY RiWdY][',
	    'Po^IkI R^NlN R^RkR R^VkV R^ZlZ R^D^I R_M_[ RdCdI ReNeZ RkDkI RWMRQ RYTZX ReJeN RWCSL R`J\\Q RSTR[ RVTWZ RYNZRRR RYHWLRL',
	    'PoUF_F RRJ`J RTN`N R_W_[ RVDRG RbFdKgNlP RgUkW RdXkZ RcCbGjGhJeM_O RUHSN RYHXN RUH^H]P RYYU[ R]PYS]U RcRYWiW',
	    'Po\\FlF R`QgQ R\\UlU RVUWZ RdSd[ R\\I_K RiNlQ RcCdF R_N\\Q RfK`Q RkIiK RVNRQ RYTZY RWCSL RYHVN RcHaM RfNgR RTUS[ RWKRL RYNYRRR ReL`M',
	    'Po]IlI RRP[P R]PcP RfPlP R]UcU RfUlU Ri[l[ RWOW[ RdCdI RfLlLl[ RRHVLSO RZRZW RYL[Q RVCTI RYFWL R]E]I RTSRY R]LcLc[`[ RlElI R]L]T\\Z RfLfUe[',
	    'PoTD^D RVH]H RUJ^J RTO_O RRVjV RTDTO R_V_[ RhHkGlH RWFVH RZJVM RbKdNjP RYK]M RdSkW RVR\\T R\\QXR RaJiJfNaP RcQYV RYXR[ RbDhDhH RdXkZ RZGXL RbDaI',
	    'Po^NjN RRP[P R^QjQ R]TkT RWOW[ R^J^Q RdJd[ R^JjJjQ RdTjZ R^F`H RcFeH RhFjI RjChF RXIVM RSHVLSO R`C]F ReCcF RZS[W RYL[Q RVCTI RZFXK RdT_Y\\Z RTSRY',
	    'PoRPZP RWOW[ R\\DbDbM RdDkDkM RiIfK R_F\\H R`I\\M RgFeH RZRZW RXLZQ RcNlQ RVCSHVKTO RYFVM RbO[R RcR^T RfT_W RTSRY RiVdY][',
	    'PoUO^O R_QkQ RRS^S RTY]Y RTUT[ RYOYY R]U]Z RfQf[ RTHXK R`JdM RhMkP RgJaQ RYCTH RdGaJ RiTkY RVMTR RbT_Y RVGXG^FYKRN RjD_F',
	    'PobJkJ RRPZP RcTkT RbZlZ R\\[_[ RWOW[ RcNcT RgIgZ RcNkNkT RjGkK RRHVKTO RYL[Q RjVl[ RVCTI RYFVM RfCcJ RTSRY RZRZW R\\K\\QaPaU_[ R\\DaDaK\\K',
	    'Po^GjG R^JjJ RRLWL R^NjN RVUWZ R^D^N R^DjDjN RfOf[lZl[ R]Q`R RbObW^[ RjPhS RVMRQ RYTZY RfTkV RWCSL RYGWM RbT\\V RSTRZ RYNZRRR',
	    'Po\\GeG RZKlK RRPZP RVOV[ R_K_X R`CaKbX RSHVK RiEkH RYRZW RXLZQ RVCTI RXFTP RSSRY RdOdT R\\O\\T ReW[Y RkPhVd[ RgDgQi[j\\lX',
	    'PoRE_E RSJ^J RSL^L RRN_N RSVjV RSGSL RXCXP RSG^G^L R_V_[ RbKdNjP RdSkW RWR\\T R\\PXR RaJjJgNbQYWSZ RdXkZ RbDhDhHlGlH RbDaI',
	    'Po^MkM RWUWZ R^F^M R^FkFkM RbQ]V RWMRR RcCaF RZTZX RgOlQ R`TcWj[ RaHhK RVCRL RgH`K RbO]Q R`ThTeX][ RSTRZ RYHWLRL RYNZQRR',
	    'Po[IlI R]LjL RRP[P R_SiS RWOW[ RdCd[ R]EjEjL RkNk[ RbShY RgOfR RbV^Z RRHVLSO RZRZW RWJVM RZL[Q R`OaR RdRaW RWCTI RYFWK RTRSY R]N]U\\Z',
	    'PocJkJ RRPZP RVOV[ RaWa[ RRHVK RjDkG R\\L_P[T RePbT RYRZW RXLZQ ReVf[ RiVk[ RfMdS RVCTI RXFUN R]IbK^R R^V\\[ RgCgJjS RSSRY R^C\\L R^GbG`P RgGfQ',
	    'Po]HbH ReHkH R\\QkQ R\\VlV R`N`U R]DbDbH RgNgU ReDkDkH RVNRQ RYTYX RfXkZ RfDfLkKlL RWCSL RaX[[ RSTR[ RVUWZ RYNZRRR R]D]LbKcL RYHWMRL',
	    'Po\\FkF R^LkL R^OkO RRP[P R]SkS RWOW[ R^I^O RdCdR RbVa[hZh[ R^IkIkO RjVkZ RhPkS RRHVLSO RZR[W RWJVM RYL[Q RbTfV RVCTI RYGWK R^V\\[ RTSRY',
	    'Po\\HkH R_OhO R`XgX Rg[k[ R\\Q\\[ R`J`O R`T`Z R^KhKhO R\\QkQk[ RfHkM RVNRQ RaH\\N RYTZX RWCSL RYHVN R\\EcE_L R`TgTgX RSUR[ RVTWZ RWKRL RYNYRRR RiDbE',
	    'Po`EgE RRP[P R]PjP R^WiW R^ZiZ RWOW[ R^I^P R^S^[ RdJdP R^SiSi[ R\\JkJjP RhFlJ RRHVLSO R_G[K RZR[W RYL[Q R`L`N RgLfN RVCTI RYFWK RTSRY',
	    'Po]HjH R]KjK R[MlM R]QbQ R]UbU R]M]Y R]D]K RbMb[ R]DjDjK RgVl[ RjUd[ RVNRR RYTYX RVUWY ReQiY RdQlQhX RVCRL RYHVN RTUS[ RWKRL RXNYRRR RdW[X',
	    'Po[GdG RZMlM RRPZP R\\TcT R\\XcX RVOV[ R\\P\\Z R]H]L R`C`G R\\PcPcX RRHVK RiFkI RYRZW RXLZQ RVCTI RXGSP RbHbL RSSRY RjPhVd[ RgDgPh[k\\mX',
	    'Po]FlF R^JkJ R]MlM RRP[P R\\RlR R_ZjZ RWOW[ R_U_[ RdFdR R_UjUk[ RiNhQ RSHWLSO RhCgF RZR[W RYL[Q R`N`R RaDaF RVCTI RYFWL RTSRY',
	    'Po[GcG R[KdK RRPZP R\\RbR R\\VbV R`[b[ RVPV[ R\\N\\[ R]C]K RaCaK R\\NbNb[ RRHVL RUMSP RYRYW RXMZQ RgRjZ RiQeZ RVCTI RYFVL RfMiX RjLgX RSSRY ReJhV ReJkJhV RfCdO',
	    'Po\\HlH RRPZP ReQlQ RcWlW RWOW[ RdCdN ReQeW RiOi[ RRHVK R^D`G RjDgG R]TaV\\[ RUMSO RYRZW RYLZQ RdHgKlM RVCTI RXFUM R_O]T R^RcQ`X RdH`L\\M RSSRY',
	    'PoRP[P RfSlS RaYhY Ri[k[ RWOW[ R]O][ RaUaZ ReIeN RfOfS RaUhUhY R]OlOl[ R]IlIiL RRHVLSO RZS[W RYL[Q RWCTI RYGWK R_DjDeG RdJ]M RcObS_S RbFhG RTSRY',
	    'Po\\KlK RRP[P R^VjV R^ZjZ RWOW[ R^R^[ R]EdEeQ RdRdZ R^RjRj[ RdJjP RdJ]Q RiFgI RSHWKVMSO RZSZW RYL[Q R_G`I RVCTI RYFWK RTSRY RiDaE',
	    'Po]FkF RRP[P R_PiP R_TiT R`[d[ RWOW[ R_L_S RdTd[ R^MjLiS RhVkY R]I_K RfHlN RkHhK R_V\\Z RRHVLTO RaI\\N RZRZV RYL[Q RVCTI RYFWL RcC`K RTSRY',
	    'PoSF_F RfGlG RTI^I RRM`M RaNkN RaRkR R`VlV RV[Y[ RYTYZ RaJaR RfCfJ RfRf[ RaJkJkR R\\V_Y RVWRZ RSU]S^U RYCYLTQ[QST',
	    'Po]GkG RfMjM R^PjP R^RjR R\\UlU Rc[g[ RWUWZ R^J^R RgFgM RgRg[ R^JjJjR R_WaY R`DbF RbGbL`N RWMRR RhCfH RZT[Y RWCTJ RSUR[ RYHXLRL RZN[RRR',
	    'Po\\HcH RfHlH R\\LcL RfLlL RRPZP RaSgS RaVgV Ri[l[ RWOW[ R\\E\\[ RaOaY R\\EcEcL RfEfL RfElEl[ RRHVKTO RYRZW RYLZQ RVCTI RYFVM RSSRY RaOgOgZeZ',
	    'Po^FjF R]IlI ReMkM R]McM RRP[P ReQlQ R\\TlT Rg[l[ RWOW[ R`K`Q RdCdI RgTg[ RhKhQ RlXl[ RSHVLSO RZS[W RZL[Q RVCTI RYFXK RaT`Y[[ RTSRY RcP]Q',
	    'Po]IcI RfIkI R^OkO RRP[P R^SkS R]WlW RWOW[ R]D]I R^L^S R]DcDcI RdLd[ RfDfI R^LkLkS RfDkDlI RRHWKVLSO RZSZW RYL[Q RVCTI RYFXK RTSSY',
	    'PocPhP RWS`S RVVcV R[[_[ R_V_[ RdKdO R[DZIdHdI R]K^M RWJZK RZVaVhUjX R]DaE RdJgK RjLkO RfEjI RaL_O RWFTI ReThU RdXkZ RSLRO RWKWO[N\\PVS RYXS[ RbRTV',
	    'Po[HlH R]LjL RRP[P R]TkT RfWkW RWOW[ R]N]XaWa[ RdCd[ RfWf[ R]EjEjL RkNk[ RSHVLTO RZRZV RYL[Q RVCTI RYFWL RfNgRkR RTSRY RaNaR]R\\Z',
	    'Po_IjI R\\KlK RRP[P R_QjQ R_TjT R_WjW RWOW[ R_E_I R_N_W RdCdK R_EjEjI R_NjNjW RSHVLSO R[R[W RWJVM RYL[Q RgYl[ RVCTI RZFXK RaX\\[ RTSRY',
	    'Po\\HlH R^KjK R]NjN R\\QlQ R^VjV R^YjY R[[l[ RVUVZ R^S^Y RdCdP RdSdY R^EjEjK R^SjSjY RUORR RYUYY RVCRL RYHUO RTUS[ RWKRL RYNYRRR',
	    'PoRElE RVLhL RRPlP RYTaT R\\[_[ RZCZG R_V_[ RdCdG RXW`VfUhV RcPkT RcXjZ R]N[QRT R^QYS RZYTZ RYN]N RcSVV RbNeN RZJVIVNZN RYJ\\JYJ RVHhHhNdN R\\J`JcJ R_NbN R_J\\J ReJhJeJbJ_J R`N\\N RcN`N',
	    'Po]ElE R^HkH RRP[P R\\SlS R_ZjZ RWOW[ R_U_[ RdCdH R^KjKjO R_UjUj[ RSHVLSO RZS[W RYL[Q RaOaR R^OiOgS RWCTI RYFWK RTSRY R^K_O',
	    'PocJkJ RRPZP RcTkT RbZlZ R\\[_[ RWOW[ RcNcT RcDcJ RgJgZ RcDkDkJ RcNkNkT RSHVKTO RZRZW RXLZQ RjVl[ RVCTI RYFVM RTSRY R[DaD`K\\K\\QaQaV_[',
	    'Po]EkE R^KbK RfKjK R^MbM RfMjM RRP[P R^QjQ R\\TlT R\\ZkZ RWOW[ R\\H\\K RdEdN R\\HlHlK RgVl[ RSHVLSO RZR[V RYL[Q RVCTI RYFWL RbT_Z RSRSY',
	    'PobFjF R`JkJ RaNkN RaRjR R`UlU Rb[l[ RfCfJ RfNfY RbKdM RiKhN RVNRR RYTYX R\\E^I R[N^N^XcZ RVCSL RYHUO R^XZZ RSUR[ RVUWZ RWKRL RYNYRRR',
	    'Po]IbI ReIkI RRP[P R]QbQ ReQkQ R]VbV ReVkV Rb[l[ RWOW[ R]L]X R]E]I RbEb[ ReEeY R]EkEkI ReMkMkV RRHVLTO RZS[V RYL[Q RVCTI RYFWL RaL\\M RlYl[ RTSRY',
	    'PoRP[P R]QkQ R^WjW R^ZjZ RWOW[ R]L]Q R^T^[ RdLdQ R^TjTj[ R]LkLkQ RdCjI R`IhHlI RRHVLSO RcC\\J RZR[W RYL[Q R`M`P RgMfP RVCTI RYFWK RTSRY',
	    'PoRE^E RSK]K RRL_L RRVjV RXCXO RSG]G]J R_V_[ R\\PXR RbKdNjQ RdSkW RVR\\T RaJiJfNaP RcQYWRZ RdXkZ RbDhDhHkHkG RbDaI RSGSK RSI^I RTNTP^P^N',
	    'Po\\FkF R[NlN R`XgX R]ZjZ R]Q][ RdCdN R`TgTgX R]QjQj[ R_JaL RhIlM RVMRQ RYTZX RhHfL RWCSL R_H]L R`T`X RSTR[ RVTWZ RXNZRRR RYHWMRL',
	    'PoRFlF RbThT Rg[j[ RTJT[ RYSYZ RZCZI R_J_[ RbObT RdCdI ReLeX RbOhOhT RTJjJj[ RUS\\R]T R[NYQ RXLWOYPWS R\\U]X RWUVY RgVhYaY',
	    'Po]ElE R^JjJ R^MjM R]OlO RRP[P R^UjU R^XjX R\\ZlZ RWOW[ R^R^X R^G^M RdGdM RdRdX R^GjGjM R^RjRjX RRHVLSO RZS[W RWJVM RZL[Q RVCTI RYFXK RTSRY',
	    'Po]IkI R\\LlL RRP[P RWOW[ R]D]I RaVaZ RbDbI RfDfI R^NjNjS R]DkDkI RRHVLSO RZRZV RYL[Q RdSfWiZl[ RVCTI RYFWK ReS\\W R^N^SjTfW RTSRY RfY][',
	    'Po_IhI R]PbP RePkP R\\SkS R]K]P R_D_I R]KbKbP RdQd[ ReKeP R_DhDhI ReKkKkP RdTkZ RSIVLSO RXSYX RYM[Q RVCSI RYFVL RdT^X[Y RSRSY RVPV[ RSPZO',
	    'Po\\FlF R`[h[ RVUWZ RaUa[ RaDaH RgDgH RhYh[ RbTeW RjUkY RbM]Q RfNaS RVNRR RZTZY R_MgQ RVCRL RYHWN R_H]M RbJ`O R]U\\Z RgJdQ RSUR[ RiSfS R^KjKiS RXKRL RYNZRRR',
	    'Po\\ObO ReQkQ R\\TbT RdUlU RVUWZ R[KaJ`Z RcJc[ RhMh[ RVNRQ RYTYY RVCSL RYHVN R^FaF_L RhFeK RSTRZ RfNdT RhKjKhK RWKRL RYNYRRQ R^C\\J\\U[[ ReDkEjK',
	    'Po\\JbJ R\\NbN RRPZQcQ R^[a[ RVPV[ R\\F\\M R_C_F R\\FbFcM RRHVKUMSP RYRYW RXMZQ RgRjZ RiQeZ RVCTI RYGVL RfMiX RjLgX RSSRY ReJhV ReJkJhV R_N^Q\\Y RfCdO R^UbUb[',
	    'Po_EkE RTH\\H R_IlI R_MlM RUN]N RUS`S R\\ViV RUVcV R[[_[ RTDUKUN RTD\\D\\H RUK]K]N R_U_[ ReIeO ReCeF RfSjW RdWkZ RhGhI R\\PVR RZXS[ RbQTV RTGSN',
	    'Po\\FlF RaMgM RRP[P R^PkP R^UjU R^XjX R[[l[ RWOW[ R^H^P R^R^X RaJaM RdCdF RaJgJgM R^RjRjX R^HkHkP RRHWKVLSO RZSZW RYL[Q RVCTI RYFWK RTSSY',
	    'Po]FkF R\\JlJ RRP[P R^PjP R^SjS RXOX[ R^M^S RdCdF R^MjMjS RbTfV RiVlY RRHVLSO RZS[W RYL[Q R`G`I RaVa[hZh[ RVCTI RYGWK R^V\\Z RhGgI RTSRY',
	    'Po]HjH R]LjL R]NkN R_QjQ R]TkT RVUWZ R]E]L R`T`[ RbCbL RfCfL R]EjEjL RVMRQ RYTZY RdUhYl[ RVCSL RYHVN RjUfW RSTRZ RdZ_Z RWKRL RYNZQRR R]N]U[[',
	    'PobIiI RRPZP Rb[l[ RVOV[ RhOhW RRHVK R[J^L RfNlT R\\D_G RiDkI RfIaN RdP`Q RfNbQ R\\P^P^WZ[ RYRZW RXLZQ RVCTI RXFUN RbE`I RkLgPcT`V RSSRY ReDiD R^P[P RgXcX',
	    'Po]IkI R^LkL RRP[P R\\PlP R]TlT R^WkW RWOW[ R]D]I RaPaT RbDbI RdIdO RdTd[ RgDgI R]DkDkI RRHVLSO RZS[W RWJVM RYL[Q RhPgT RWCTI RZFXK RTSRY',
	    'Po]OcO R\\ScS RaWa[ R]KcKcS RiOdT RUNTR RYUYX ReWf[ RjWl[ RfJjR RjJgR RWCSL RYHUP R_C\\J R^GbF`K R]V[[ RVUWZ RfHhP RfHkHhQ RSUR[ RgDeL RWKRL RYNYRRR',
	    'Po]EkE R^JbJ RfJjJ R]MbM RfMjM RRP[PlQ Ri[k[ RWOW[ R\\H\\K R]T][ RbSb[ RdEdN RfTf[ R]TkTl[ R\\HlHlK RSHVLTO RZRZV RYL[Q RVCTI RYFWL RTSRY RcPbV',
	    'Po^LdL ReLlL R\\NlN RRPZP R^XdX ReXlX R\\ZlZ RVOV[ R\\C\\[ RcUdX RRHVK ReRhU RjUlX RcJdL ReGhI RjJlL RjGeL R`P_SaT^X RhPfS RjSeX RbFaI R`D_GaI^L RhDfG RYRZW RXLZQ RVCTI RXFUM RcRaU RSSRY',
	    'Po_HiH RRP[P R_RjR R_TjT R_WjW RWOW[ R]F]H R_N_W RdCdF RdHdM R\\OjOjW R]FkFkH RRHVLTO R`J^M RZS[V RYL[Q RhYl[ RVCTI RYGWM RaY\\[ RTSRY RiJeM]O',
	    'PoaIkI R`KlK RbQjQ RbXkX Rb[l[ RWOW[ RaEaI RbNbW RfCfK RbNjNjQ RbTkTkW RaEkEkI R\\J^L R\\D_F RRPZP_P_W[[ RZRZW RYLZQ RVCSIVKTO RYFVM RTSRY',
	    'Po\\DdD RfIlI R\\LcL RfOlO RRP[P R\\PdP R[ZlZ RWOW[ R\\D\\P R]S]Z RaLaP RaSaZ R\\HcHdL RfSfZ R]SjSjZ RRHVLSO RZS[V RYLZQ RVCTI RYFWL R`DaH RTSRY RgDeK',
	    'Po^IlI R_NkN R`QjQ R^UlU RVUWZ R]F][ RcCcI RgCgI ReNeUfWj[ RbKcN R`EaH RhKgN RdV`[ RVNRQ RYTYY RjEiH RVCSL RWKUP RYHVN R^DZL RSTRZ ReQcX RWKRL RYNYRRQ R]C]K',
	    'Po\\GlG RRP[P R]OjO R]RkR R\\UkU RWOW[ R]J]O RaXa[ R]EeEeU R]JjJjO RiWlZ RSHVLSO RZR[W RYL[Q ReWf[ R`L`N RgLgN RVCTI RWJVM RYFWK R]W\\[ RTSRY RjDcD',
	    'PoUF^F R`FlF RVJhJ RVMhM RVOhO RRQlQ RVHVO R_W_[ RVHhHhO RVDRH RaD]H RbQeSlV RVXfWiX RdYj[ R]RZU^V R\\OZRRU RaS_V[W RZYTZ',
	    'Po[FlF RfOjO RRP[P R^WjW RWOW[ RdJdO RdCdF RgIgN R^RjQj[ RfGeJ RSHVLTO RZR[V RYL[Q RbGbJ RiIlO RVCTI RYFWL RkIgJ RTSRY R^I\\N R\\IaI`O^N R^P^U]Z',
	    'Po[FlF RaQjQ RbSjS R`VkV Rb[l[ RVUWZ R`C`H RfOfY RgCgH R\\O^O^X[[ RdH`K RYUYY R]I]L RcKkN R^Xd[ RVCRL RUMSR RXGTP RbJiJgLaO RSUR[ RWKRL RYNYRRR',
	    'PoaKeK RaPeP RSRYR R\\SlS RVTVZ RaFaP RcQc[ RaFeFeP RbCbG RXNYS RcRhXlY RkHgP RVCRL RXHTP R]D[J R_H[P RiDgJ RcR^XZY RSUS[ R_J[J RkJgJ R_M_P[O RXTYX RWKRL RkMkPfO',
	    'Po^KkK R^MkM RRP[P R]PlP R^WjW R\\[l[ RWOW[ R^D^K RdDdP RdTdZ R^DkDkK RfRgT RiRlT R^R]T RRHVLSO RZSZW RYL[Q RbRbT R`FaI RVCTI RYFWK RgFgI RTSRY',
	    'Po_ElE RRH]H R`MjM R`RjR RRU]U R`VjV RXCXM RXUX[ R`I`V ReEdI R`IjIjV RgXjY RTDTG R[S^V R[W]Y RXHSM R\\D[G RZPUU RUWRZ RWI\\K RWOTQWS RbY^[',
	    'PocDlD R[HcH RdMkM R[MbM RRPZP RdRkR RdVkV R\\XbX RVOV[ R\\Q\\Z R_C_M R\\QbQbX RdIdV RgDeIkIkV RRHVK RiXl[ ReXb[ RYRZW RXLZQ RVCTI RXFUN RSSRY',
	    'Po\\EcE ReEkE R\\HcH RdHlH RRO[O R^QjQ R^TjT RWOW[ R^NjNjW R`C_JcK RhHdM RRHVLSO R_J\\M RZR[W RYL[Q R^N^WhXlZ RhGjLlM RVCTI RYGWK RaY\\[ RSRSY R`E_L RhDgK',
	    'Po\\FlF R\\IlI RaLhL R\\NlN RRP[P R_TkT R_WkW RWOW[ R_Q_W RaFaL RdCdF ReNeW RhFhL R_QkQkW RSHVLTO RZS[W RYL[Q RhYl[ RVCTI RYFWL RbY^[ RTSRY R\\F\\S[Z',
	    'Po\\DlD R^JiJ R^LiL RaSgS R_ViV R\\N\\Q RcEcG R^GiGiN R^G]NlNlQ RbOeP RiQkS R^Q\\S RVMRQ RYTZY RVCRL RYHVN R`QaT`V\\W RVUWZ R`WeYk[ RSTRZ RWKRL RXNYRRR RhWcZ[[',
	    'Po\\ElE R^HkH RfLkL R]NkN RRP[P R^SjS R^UjU R^XjX RWOW[ R]J]N R^P^W RdCdH ReJfL R^PjPjW R]JkJkN RSHVLTO RcJ`L RZRZV RYL[Q RVCTI RYFWL RgYlZ RTSRY RaY[[',
	    'PoVFhF RVIhI RSM]M RaMkM RSP]P RaPkP RRWjW RSKSO RVDVI RXKXO RSK]K]O R_D_I R_V_[ RaKaO RfKfO RVDhDhI RaKkKkO R\\QXR RdTlW RdQYV RYXR[ RVR]T RdXkZ',
	    'Po\\GdG RZJlJ RbNeN RRPZP R[U^U RVOV[ R_J_Y R`CaJbX RiEkH RhUb[ RYRZW RXLZQ RjOfW RVDSHVKTO RXFUM RlWk[ RSSRY R\\N^N[N R\\R^Q[Q ReRbR ReUbU RgCgMgPiZ RfXZY',
	    'Po\\FlF R_JkJ R_MkM RRPZP R_OkO R^RlR RgUlU R_WkW R]ZlZ RWOW[ R_H_M RdCdF ReUeZ ReHfRgU R_HkHkM RcR`U RZRZV RYL[Q RVCSHVKTO RYFVK RSRSY R\\F\\S[Z',
	    'PocDlD R[HcH RdMkM RRPZP RdRkR RdVkV RVOV[ R_C_G RdIdV RgDeIkIkV R\\QbW RRHVK RiXl[ ReWb[ RYRZW RXLZQ RaJbM RVCTI RXFUN R]J[N R`P\\Y RSSRY',
	    'PocEkE RcMkM RRPZP R`QjQ R`TjT R][l[ RWOW[ R_V_Z R`N`S RcVcZ ReNeS RgVgZ R`NjNjS R_VkVkZ R]HlHlJ RRHVKTO RZRZW RYL[Q RVCTI RYFVM RTSRY RfJaJ R]H]R[Z RdCdK_K RiJcJ',
	    'Po\\DlD R_IjI R_LjL R_NkN RRPZP R^QlQ R_WkW R]ZlZ RWOW[ R_G_L RcRcT ReGeQ ReUeZ R_GjGjL RiRlT R`R^T RZRZV RYL[Q RgRgT RVCSHVKTO RYFVM RTSRX R\\D\\S[Z',
	    'Po]GbG ReGkG R]JbJ ReJkJ R]MbM RRPZP R\\SlS RWOW[ R]D]L R]DbDbL ReDkDkL ReDeMjMlP RfNdP R^N\\P RZRZW RXLZQ RVCSHVKTO RYFVM RaQ_Wk[ RTSRY RhUdY\\[',
	    'Po]JjJ R^LcL ReOlO RRP[P R^WkW Re[l[ RWOW[ ReJeO R\\HjGjJ R\\TkTjW RSHVLSO R_EfEcG RZR[W RWJVM RZL[Q RgXjY RVCTI RYFWK R`P]S]W ReWe[ RTSRY RjLeM R_QfQdScXaZ[[ R`C\\G^N RcN]O',
	    'Po\\FlF R]KbK ReKjK R\\NkN RRPZP R]QjQ R[TlT RWOW[ R`V`Z R`M`T R]HbHbL RdCdF RgMgT ReHjHjL RZRZV RXLZQ ReUhYl[ RVCSHVKTO RYFVM RdT[X RkVfW R]H]L ReHeL RTSRY RfY]Z',
	    'PoZKlK RRPZP RVOV[ R_K_Y RbKbY RRHVK R]F_H RbFeI RiEkH RYSZW RXLZQ RVCTI RXFTP R^C[J RcC`I RSSRY R\\N_N[N ReNbN RjOgWdZ[Z R[R_R RbReR RbUeU R[U_U RgDgRh[k[mX',
	    'Po^GkG R_LkL R_OkO R_QlQ RbZhZ Ri[l[ RWUWZ R^S^[ R^D^G R_J_R RbWb[ ReIeQ RbWhWhZ RkDkG R^SlSl[ RbU_V RWMRR R`H\\L RZT[Y RfTjV RVCSL RSURZ RYNZQRR RdCdGdI RYHXLRL',
	    'Po\\GbG ReFkF RdIlI RRP[P R^QjQ R^TjT R^WjW RWOW[ R^N^W R^NjNjW RSHVLTO RZR[V RYL[Q RgYl[ RVCTI RYFWL R]D\\G R_D_I\\M ReDdG RhDgIeM RcL`L RaY[[ R[IbI`M RTSRY RlMiM RhFiM',
	    'Po]IkI RdNlN ReRlR ReVlV ReZlZ RVUWZ R]D]I RbDbI ReMe[ RfDfI RiLiY R]DkDkI R`P\\T RVNRQ RYTZY RbVcY RVCSL RYHVN R_J]O ReKdQ R_V`Z RTUS[ R\\V\\[ RWKRL RaN\\O RYNYRRQ RaL`QbT\\T',
	    'Po[DbD RdDlD R\\MlM RRPZP R\\TkT R_WdW RVPV[ R\\G\\K R_I_K R_T_[ RaMaT R\\GbGbK ReGeK RhIhK ReGkGkK R\\PkPkT RfMf[lZl[ RRHVKUMSP RYRZW RXMZQ RVCTI RYFVL RkVfW RSSRY R\\M\\U[[ RdZ][',
	    'PoTDjD RVFiF RRHlH RRLlL R`WkW RSW^W RSPSW RYWY[ R^J^N R_C_H RfVf[ RWJgJfO RiXl[ RaSeT R[X]Z RiTlW RUXR[ RePbS RaQhQcV RbX`[ RYQVPVT[R]QYQ RVUZU RWJVNjOcP RVO]O]UZU',
	    'Po[DbD RdFlF ReGkG R[JbJ R[MbM RdMkM RRPZP R]RjR R]TjT R]WjW RWOW[ R[D[M R]O]W R^D_G R_J^M R[GbGbJ RdJdM RfJfM RiJiM R]OjOjW RdJkJkM RRHVLTO RYRZV RYL[Q Rf[lZl[ RVCTI RYFVM ReCcH ReWf[ RSRSY RaW_ZZ[',
	    'Po^FiF R^IiI R\\NbN ReNkN RRPZP RePkP R\\VkV RVOV[ R^D^I R_K_P R\\KbKbQ RdDdI RdVd[ ReKeP RhKhP R^DiDiI ReKkKkP RRHVK R\\K\\PcP`S R_X\\[ RYRZW RXLZQ R^RcT RgXkY RVCTI RXFUN ReT`V RTSRY',
	    'PoaDeD RaIeI RaKeK RRPZP RaQeQ R`[g[ RVOV[ R[N[R R[L_L^R R`U`[ ReMeQ RgNgQ RgXg[ RfLkLjR RkMlQ RRHVK RhVkY R[G]I RaGgGiI RiDhG RYSZW RaMaQ R_I_L RkIlL RaSdT RjFhK RVCTI RXFUN R^F\\K R]V[Z RaMeM RSSRY R]D\\G RYLYP',
	    'PoRN]N R\\DRN R`KZQWRRU R[TRU R^TXU R`YS[',
	    'PoRMZM RbFbU RlCl[ RYFRM R\\JST RlS`U R[RRT R]WRY',
	    'Po^EkE R\\NlN R`[d[ RdEd[ R[JXMRS RWCTL RYLRM R[RRT R\\WRY',
	    'Po]GkG R][l[ RdGd[ RTJSN RXNRU RXDTL R[TRU R[YRZ R[JYMRN',
	    'Po]JlJ Rc[h[ RhCh[ RYMRS R`NcT RWCTL RZJZK RYLRM R[RRS R\\WRY',
	    'Po]NlN ReEe[ RVIRM R[KRS RXCVI R\\WRY RjD^F RZLRM R\\RRT',
	    'Po`IlI R_[k[ RlWl[ RVIRM R[KRT R_MiM^X RXCUJ RaC]M R[XRY RYLRM R[SRT',
	    'Pod[j[ RaNfS RVIRM R[JRS RXCVJ RcC^M R\\WRY RaIlIlSj[ RZLSM R\\RRT',
	    'PodTk[ RZJXMRS R`E`MeV RWCTL ReNkMgUcY^[ R_O\\Z R]EhEeM R[RRS R[VRX RYLRM R`J^W',
	    'Po_W[[ RZJXNRS R]OeS RlYk[ RWCTL RaR]Y RYLRM R[RRS R\\WRY R\\IhIhSiZ RbCbL`V',
	    'Po_GlG RdCfE RZJXNRS RWDSM R[VRX RYLRM RZRRS R_G_Q]Y',
	    'Po_OjO R_[k[ R`O`[ R^EjEjP RlWk[ RUJRM RZLRT RXDUJ R[XRY RZJYMRM R[SRT',
	    'Pod[i[ RbU][ RZKRS RUHSM RXCTK RdPaX R`J^R RYLRM R[RRS R\\WRX R^FlFlPjZ ReFeKcT',
	    'Po]GkG R^LjL RdCd[ RWNRS RWCTL RkXgX RZIXMRM RZRRS R[WRX R\\QlQkX',
	    'Po^FjF R\\NlN RdYkY R[JXMRT RhSl[ RUISM RXCTK RdN^Y R[RRT R[WRY RYLRM RhXcY]Z',
	    'Po]IlI R[JXMRS RhSl[ RWCRM R`N\\[ RfLbZ RYLRM R[RRS R\\WRY RaCaH^W RiYaZ',
	    'Po]GlG R^SjS Rd[l[ R^K^S ReCe[ RjKjV RlXk[ R[JXMRS RWDSM R[RRT R\\WRY RYLRM',
	    'Po]MbM Re[l[ R]D][ RfDf[ RlXl[ RWORT RWDSM RlKeN RbX][ RZWRY RYJXNRM RZSRT',
	    'PodCdS RUIRM R[JYMRS RiHlN RXCUJ RiReX][ R_H^P R\\WRX RYLRM R[RRT',
	    'Pog[l[ R]E][ R]ElEl[ RZIYMRS RcLeOiV RWCSM RhHaW RYLRM R[RRT R[WRY',
	    'Pog[l[ R^J^[ R^JlJl[ R[JYLRS ReCeMhPiU RXCTK RdMaT R[RRS R[WRX ReGcQ RYLRM',
	    'Po]OlO R][l[ ReEe[ RXNRT RWDRN RZJXMRM R[SRT R[XRY RjD]F',
	    'PoWNRS R_D_O`QbU RfRbZ RWCSM R^SZ[ RgDgOdX RZWRY RgKiX RYIXMRM RYRRT R_J]W RgJeV',
	    'Poa[j[ RaMa[ ReFlM RdDfH RjXj[ R[JXMRT RcG]M RXDTL RdCaI RiP`T RYLRM R[RRT R\\WRY',
	    'Poc[g[ RaW\\[ RYLRS RfEjM RXCTK RbD^M RcO`X R[XRY R^OiOiTh[ RYLRM R[RRS',
	    'Po^NkN R^F^[ RYMRS RWCTL RdY][ RfDfNgY RYLRM R[RRS R[WRX RjC^E RlWk[i[',
	    'Po\\IlI RcDdH RZJXNRS R_McUgYl[ RWDSL RgMdU`XZ[ R^IaS RhIeT RYLRM RZRRS R[WRY',
	    'Po^HlH Rd[i[ RYLRS RdDeG RWCTL R[IZK RaS^Z R[WRY RbHbOjNjUi[ RbK`W RYLRM R[RRS',
	    'Po\\[l[ R]I]N R]IkIlM RWPRT RcDdH RXDSN R[XR[ R[SRU RZJYNRN',
	    'Po^KeK R_[d[ R^DeDeK RlDl[ RVIRM RZJYMRT RXDUK R[WRY R[RRS R^K^QfQd[ RYMRM',
	    'Po[[l[ RjKjS RjHjQ R]EjEjO RWORT RXCSM RZSRT RZXRY RZJXMRM R]OiOiVh[ RbK`V RcEbLaQ`[',
	    'Po`[d[ RdMd[ R`HcIfL R[JXMRS R\\MlMiR RWCTK R^EjEgHcI R[RRS R[WRY RYLRM',
	    'PolXl[ RZJXNRS Rg[k[ RfDjF RWCSM RkQfV\\[ RcOfY RXLRM R[RRT R[WRY RcCcLdW RfI^J RhI`J RjHcI RkM]P',
	    'Po]IlI R_QiQ R_YiY R_C_[ RiCi[ RVIRM R[JRS RXCUJ R[WRY RZLRM R[RRS',
	    'Po[LlL RcUiU R^[k[ R^D^[ RcCcU RiDiU RVQRT RWDRN RZIWP RXMRN R[SRU R\\XRZ',
	    'Po\\JlJ RfDiG RgTaZ RXNRS R`C`NcUhYl[ R`NiNeW RWDTL R_S[[ RZRRS RZWRY R`I^W RZJXMRM',
	    'Po[GkG R\\QkQ R_[d[ R[KdKd[ R^T\\X RVORS RiUkY RWDRM RYIVO RaG\\P RaC^O RZVRX RXLRM RYRRS',
	    'Po_LiL R_TiT R\\[l[ R_E_[ R_EiEi[ RTJSM RZLRT RXCTK R[RRT R[WRY RYLRM',
	    'Po^NlN R^TlT R^H^U ReCe[ R^HlHlU RVIRM RZLRS RXCVI R\\WRY RYLRM R[RRT',
	    'Po^OlO R^YlY R^E^[ ReEeY R^ElEl[ RUIRM R\\JRT RXCUJ R\\XRY RZLRM R\\RRT',
	    'Po^PkP R^E^P R^EkEkP R[JYLRS RhTk[ RXCTK R`T]Z R\\RRS R\\WRX RYLRM',
	    'Po`GiG RYJXNRS RaC`G\\J R`HfOlQ RbRgT RWCSM RhHbN\\Q R^Wj[ R[WRY RXLRM R[RRS',
	    'Po_SlS R^ZlZ R^NlNl[ RjIeN RVIRM RZLRS RaH]L RbFkFiK RXCUJ RcC`J R[RRS R[WRY RYLRM',
	    'Po]LkL R\\SlS RdCd[ RXNRS R^E`J RWDSM RjEgJ RZRRS R[WRY RZIYMRM',
	    'Po]MkM RfCf[ R]GkGkM R`W[[ RZJXMRS RWCSM RkXiX R\\M\\RlRkX RYRRS R[WRY RaCaQ`W RYLRM',
	    'Po^MkM R^ZlZ R^Q^Z R^F^M ReDeZ RkFkM RlQl[ RZIXNRT RTJSM RXCTL RYLRM R[SRT R[XRY',
	    'PofNkN R_ZlZ R_Q_[ R_QlQl[ RZLRS RcJ^N RXCTK RdFaL R\\RRS R\\WRY R]ElEkN RYLRM',
	    'Po]RjR R\\VlV RdOd[ RXNRS R^FcKkN RWCSM R\\EjEeJ\\O RZIYMRM RZRRS RZWRY',
	    'Po]RkR R\\[l[ RdRd[ RXNRT R]EiEcKlO RXDTL RdK[O RZJYMRN R[SRT R[XRY',
	    'Po^ZjZ R^Q^[ R^QjQj[ R\\MdMjLkN RgHjK RZIYMRS R`I\\M RWCTL RcC`I R[RRS R[WRX RYLRM',
	    'Po\\GcG R\\McM R[SdS RfEf[ RlRlV RUPRS RhNkPlT RfElEhM RWDRM RXIUQ R`C`T\\Z RkWgW RXRRS RYVRX RWLRM R_O^X',
	    'Po\\HlH R\\PcP RgDjF RgUa[ RWNRS RjMeX RlWk[ RWDTL R`K`R]X R[RRT R[WRY ReCeKeMgY RZIXMRM',
	    'Po]GlG R^NlN R_ZkZ R_R_[ ReCeN R_RkRk[ RYLRS RWDSL R[RRS R[WRX RZJYMRM',
	    'Po[GlG R_MhM R\\QlQ Rb[h[ RdGjM RXNRS RbG\\M RWDSL RcC`J RaQ_UiUh[ RZRRS R[WRY RZJYLRM',
	    'PodGjG RaGhG R]HdH R[RlR Rf[l[ RfRf[ RkLkO RlXl[ R`W\\[ RWORS RaFcKfNiO RWCRM RYIVO R`CbK RaR_Y RiJcM]N RYWRX RXLRM RYRRS',
	    'Po]EkE R^TkT R\\[l[ RdOd[ R^MjLkN RgIjK RWORT RaJ^M RWDRN RcEaJ RZJXMRM R[SRT R[XRY',
	    'PocEkE RbLlL Rd[h[ R_L_[ RiLi[ R_D[I R_L[P RWDRM RUOST RXIUQ R`I^N RYSRS RYVRX RWLRM',
	    'Po_MhM R\\QkQ RdYjY R`ZfZ RfUjY RcDjK R_W\\Z RWORS RcCbG\\L RWCSM RYIWO RbQ_W RYRRS RZVRX RXLRM RcZ\\Z',
	    'Po`LhL R^ZiZ R^Q^[ R^QiQi[ ReFlM RdCfH RZIYMRS RbG\\M RWCSM RcCaI R[RRS R[WRY RYLRM',
	    'Po_QfQ R_VfV Re[j[ R_L_W R_LfLfV RZIYMRS RWCRM RaC]K RYLRM R[RRT R[WRX R_HlHlSj[',
	    'Po]QjQ R\\WkW R^S\\V RdNd[ RWORS RbC\\I R`GdJlN RWCSM R`FiFeJ\\N RZRRS RZVRX RYJXMRM',
	    'Po_ZjZ R_R_[ R_RjRj[ RZIXNRS R`G\\K R`HeNlQ RWDSM RaC_I R`FkGeM\\Q RZRRS RYLRM R[WRY',
	    'Po^RjR R_[k[ R_K_[ ReKeR R^KjKjR RlXk[ RXMRT RUHSM RXCTL RaC^K R`GgFdM R[JYLRM R[RRS R[WRY',
	    'Po\\GlG RgJlO RbCeF RZJXMRS R`K\\O R_OaTfYk[ RWCSM RhNfTaX[[ RYLRM RZRRS RZWRY',
	    'Po]HlH RcOjO R]OfO Rf[l[ RgPg[ RlXk[ RgKkN RUISM RZJXMRS RdHaK]O RcCdG RXCTK R^YZ[ R`Q_X RYLRM RZRRS RZWRY',
	    'Po[ElE R\\MkM R\\RkR R\\I\\R R\\IkIkR RbU^Y RWNRS RWDSM R]TaXY[ R`Xj[ RYJXMRM RZRRS RZWRX RcEcQbV',
	    'Po_PkP R_UkU Rg[k[ R_K_[ ReCeK R_KkKk[ R_EaI RkFiH RZIXMRS RWCSM RYLRM R[RRT R[WRX',
	    'Po`JkJ R_RlR R_VlV Rg[l[ R_M_[ R`D`J R`DkDkJ R_MlMl[ R[JYMRS RUISM RXCUK R[RRS R[WRY RYLRM',
	    'Po]IlI Re[j[ R]EdEeO RdHkO R`X\\[ RZIXMRS RdH^O RWCRM R]QgQfUkTj[ RaQ`X RYLRM R[RRS R[WRX RiD`E',
	    'Po_ZjZ R_R_[ R_RjRj[ RfLlR RdHfM RgDkI RcL\\R RXNRS RaF]J RWCRM RdHbN RZIXMRM RZSRT R[WRY',
	    'Po]FcF R\\PlP RYMRS R]H^L RcGdK R_UkZ RiGhK RWCSM RbL_T RhPfUcX`Z[[ R[RRS R[WRY RjD_E RZJYLRM',
	    'Po\\RkR R`[c[ RdOd[ RgUkY R_V\\Y RWORS RbD\\I R_GdLkO RWDSM R`FiFdK\\O RZIXMRM RZRRS RZWRY',
	    'Po`MlM R^[l[ R^E^[ RfDfY ReMlT RcR`U RWPRU RaFbK RjFhJ RWDRN RfLbT RZJXNRN R[SRT R[YRZ',
	    'Po]MjM RgYkY RcHc[ R\\HjHjM R_C`G RWNRS RhEeH RcQ[Y RXCTK RZRRS R]M\\RlRkX RYIXMRM RZWRX',
	    'Po]FlF R^IkI R\\LlL R^O^V RdCdL R^OjOjV RXNRS RfXl[ RWCTK RdVaY\\[ RdRcW RZIXMRM R[RRS R[WRX',
	    'Po]GhG R\\LlL R`UjU R`ZjZ R`P`[ RcCcL R^QjQj[ RXNRS RWCSM RjGbO[R RYJXMRM RZRRS R[WRX',
	    'Po]GjG R\\KkK RcCcK RbO\\U RVORS R_N[P RfMlP R_SdXk[ RWCRM RYJVO R`RhRdX\\[ RYXRY RXLRM RYRRS',
	    'Po\\FlF R[SlS R^L^R R`C`I RgCgI R^LiLiR RVORS RcIcSeWhYl[ RWCSM RYIWO RbU_YZ[ RZWRX RXLRM RYRRS RcNbV',
	    'Po^FjF R\\TlT RdCdJ RhXl[ ReWiX R]P`Q R_MbN RWORS R]JkJiN RWCSL RdU`Y\\[ RZVRX RYIXMRM ReLePdV RZRRS',
	    'Po]GkG R[OlO R^WeW Re[i[ R^R^Y R^ReReW RiOi[ RWORS RcIjL RWDSM RcHaK\\M RdDbJ RYJXMRM RZRRS R[WRX',
	    'Po\\HaH RfHlH R\\NaN RfNlN R\\SaS RfTlT RaCa[ RfCf[ RYJXNRS RWDTL RZWRY RYSRS RXLRM',
	    'PodFlF R^NkN R^RkR R\\VlV R^J^R RdCdJ RdRd[ R^JkJkR RTJSM RYLRS RXCTK R[IZK RZRRT R[WRX RYLRM',
	    'PoaVgV Rg[l[ R\\L\\[ RaPaX RdCdL RaPgPgV R\\LlLl[ R\\E`H RlEgI RWNRS RXCTK RZRRS RZWRX RZIXMRM',
	    'Po]IjI R]MjM R]ScS Re[l[ R]D]M R]O][ ReOe[ R]DjDjM RlXl[ RZJXMRS RWDSL RkReT RcZ\\Z RZRRS R[WRY RYLRM',
	    'Po^JjJ R]QkQ R]VkV Rd[k[ R]M]W R^E^I RdJd[ R^EjEjI R]MkMkV RlXk[ RZJXNRS RXCTL RYLRM RZSRT RZXRY',
	    'Po^IlI R_NkN R_SkS R_YlY R_G_[ RfHfY RdDfG RZJXMRS R_H[L RWDSM R`D]K RYLRM RZRRS R[WRX',
	    'Po^JlJ R^NlN RiYlY R^R^Y R^F^N RdCcF ReNe[ R^FlFlN R^RlRlY RUHSM RXMRS RXCTK RZVRY RZIYMRM R[SRT',
	    'Po\\EbE R]QiQ R\\L\\O RcGcJ R\\LkLkO RiFhJ RXMRS R^G_J R_SdXk[ RXCTK RiRdW\\[ RZIYMRM RZRRS R[WRY R_Q_S RjC`E',
	    'Po\\KbK RfKlK R\\QbQ RfQlQ R^[b[ Rh[l[ R\\DbDbZ RfDlDl[ RVORS RWCSM RYRRS RYWRX RYIWNRM R\\D\\R[Y RfDfTdZ',
	    'PoaJiJ R`NjN RaWiW Rh[l[ RaRaX ReGeN RaRiRiW R^ElEl[ R[JXMRS RWDSM RZRRS RZWRY R^E^Q\\X RYLRM',
	    'Po_LhL R]PiP R^WhW Re[j[ R^R^V RcLcV RhRhV RVPRS RWCRM RYIVP R_C[L R`I^N RYRRS RZVRX R^HlHlUk[ RXLRM',
	    'Po^ZjZ R^S^[ RgCgN R^SjSj[ RhGlK RbK]Q RVPRS RWDRM RYIVO R_C\\K R^FdFaN RYRRS RYVRX RXLRM R^G`LbNePkP',
	    'Po]IjI R\\NkN RdWgW R_[i[ R`R`[ R_RgRgW R]D`G RfNlS RiDfG R`N[S RjXi[ RVORS RWCRM RYIVO RcH_Q RdC`O RYSRT RYXRY RXLRM',
	    'Po_LiL R\\QkQ R`[d[ R]G]J RdQd[ R]GkGkJ RhTkX RWNRS RcCeG RWDSM RZIYMRM RZRRT R_U[XRX',
	    'Po^LkL RdRkR Rd[l[ R\\G\\J ReLeZ R]GlGlJ R`WcZ RZJXMRS R_TaY RcCdF RWDTL R_Q]Z R[WRY R[RRS RYLRM',
	    'Po_PiP R_ZkZ R\\F\\J R_J_[ R_JiJiP R_TkTk[ R\\FlFlJ RZIXMRS RdCdE RWCRM RYLRM R[RRT R[WRY',
	    'Po^IiI R]MlM Ra[d[ RdMd[ R^EiEjM R^PaS RdRjX RhRfT RkPgS RWNRS RWCSM RcU]X RZWRY RYJXMRM RYRRT',
	    'PofVk[ R_VaY RhJkM RiIdN R`U[Z RWMVPRS R`I[N RdQgX R[HaK R[U`W RdHiJ RdEkEhK RWDRM R[EbE^L R[QbQ_W RcQkQiVfYa[ RYRRS RZVRX RYIWNRM',
	    'Po]UlU R]ZlZ R]O][ RdOdZ R]OlOl[ RbIeL RhIlL RkEhH RXMRS RdDcH R^I`M RWCSM R_C]H R[JXLRM RZRRT R[WRY',
	    'Po[GlG R_JiJ R^SjS R\\VlV R^N^S R_D_J RdJd[ RiDiJ R^NjNjS RXNRS RWCRM RZJXMRM R[RRT R[WRY',
	    'Po[IcI RdKlK RdRlR RdYlY R_C_[ RdDd[ RdDlDl[ RVPRS R_IbP RWDRM RYJUP R`I[T RYSRS RYVRX R_F]P RXLRM',
	    'Po]HlH R_LdL R_UdU R_O_W R_OdOdU RlWk[ RhDkF RgUa[ RVPRS RjMeX RWDRM RYIVP RYRRS RZVRX RXLRM RgDgOhZ R]H\\R[Y',
	    'Po\\ElE RbOgO RbTgT R]ZkZ R]J][ RbJbZ RgJgZ R]JkJk[ RWNRS RWCSM RdEcJ RYIXMRM RZSRT RZWRY',
	    'PoeGlG R]D]K R^O^V RaCaL R^OjOjV ReVeZlYl[ RgJjK RWNRS RWCTL RfDdJ RZRRS RZVRX RaYZ[ RcQbX RZIXMRM',
	    'Po^IjI R^MjM R\\PlP RdUkU RcZlZ R^E^L RdPdZ R^EjEjL R_VbZ RXNRS R^T`X RUGSM RWCTK R^S[Z R[WRY RZIYLRM R[RRS',
	    'Po\\KaK R\\QaQ R\\WaW R\\E\\Y R\\EaEaW RgDgS RjTd[ RUPRS RkQhW RWDRM RXIUQ RjGlO RXSRS RYWRY RdGcP RWLRM',
	    'Po^IjI R_PjP R_TjT R_XeX R\\XcX R^D^I R_M_X R\\MkMj[ R^DjDjI RXNRS RWDTL RZVRY RZIYMRM RZRRS RjWcW RlVfW',
	    'Po^IiI R^NiN RZ[l[ R]R][ R^E^N RaRa[ RfRf[ R^EiEiN R]RjRj[ RWORT RWDSN RZSRT RZWRY RYJXNRM',
	    'Po\\IjI R[LlL R\\E\\I R]O]W RcCcL R]OiOjW R\\EjEjI RWNRS ReXk[ RWCRM RbX[[ RZWRY RcRcW RYRRS RYJXMRM',
	    'Po^JjJ R^OjO R`[h[ R^E^O R`S`[ RdEdO RhXh[ R^EjEjO RXNRT RcReV RjSlY RWDSL R]S\\Y R[RRS RZWRY RZIYMRM',
	    'Po\\JkJ R\\PlP R\\UkU R][j[ R_J_U R^EdEe[ RiJiU RVPRT RWDRN RYJVP RYXRY RXMRM RYSRT RiDaE',
	    'Po\\JaJ RiMlM R\\PaP R]E][ RdDiDjM ReUk[ RVORS RcPgX RWDRM RYIVO RcPkPhVeYa[ RaD\\E RYRRS RYWRX RXLRM RbU[V RdEcL',
	    'Po`ElE R`MkM R`QkQ R]G][ R`I`Q R`IkIkQ R`TdX`[ RcWl[ RVDRM RXITR R^DZM RXRRS RXWRY RWLRM ReEeRdW',
	    'Po^JjJ R^OjO Ra[d[ R^F^O RdOd[ R^FjFjO RfTlZ RaU\\Z RjQgU RZJXMRS RdPhW RWCRM RcCdF R]RbR`W RYLRM R[RRT R[WRY',
	    'Po_JlJ RaOkO R_TlT R]G][ RaEiEiJ RgWk[ RdV`[ RUOSS ReRhY RWCSM RXJUQ R^CZM RbK_R RYRRS RYVRX RWLRM RePdW',
	    'PoaLjL RaWjW Ra[l[ RaGaW ReCdG RaGjGjL RaQjQjW R[E^H RZM^M]XZ[ RVPRT R]Xc[ RWDRM RYJWO RYSRS RZWRY RXMRM',
	    'Po\\KkK R[NlN R_RiR R\\EcEcI R]WY[ RZJXMRS R^F^I R_SdXk[ RWCTL R_Q[Y RiFgJ RhTdX^[ R[WRX R`K^V R[RRS RjCbD RYLRM',
	    'Po]FlF RgYjY R\\L\\O R^Q^X RdNd[ RdCeF R^QkQkX R]LlLlO RZIXMRS R`GaK RWCTL RhGgL RZRRS R[WRY RYLRM',
	    'Po\\IlI R\\SlS RdCdO R_D`H RjDhG RWNRS RdHhLlN RWCSM RbP`U RdH`L\\N R_Wl[ RZRRS RZWRY RhSgVdY][ RYIXMRM',
	    'Po^KlK R_UlU R^F^R_[ RdOdZ RhOhZ R^FlFlK RWNRS RdCfF RWCSM RZVRY R_OlOl[j[ RZRRS R^N\\Z RZIXMRM',
	    'Po]HjH R]KlK R]VjV R]ZjZ R]D]P R]R][ R]RjRj[ R]DjDjH ReMiP RdIgO RWNRS RWCSM RcO\\P RZWRY RZRRT RYIXMRM RlNlQiP',
	    'Po^IgI R[LlL RfTfZ RTJRM RWORS RhRkY RXDUJ ReL\\Q RdQ\\U ReTbW[Z R_C^I RYRRS RZVRY RkOdReX R_EhEgL RZIXMRM Rd[`[',
	    'Po\\ElE R\\NlN R^UjU R^ZjZ R^Q^[ RbEbN RfEfN R^QjQj[ RZJXNRS R^G_K RjGhL RWCSM RXLRM RZRRS R[WRY',
	    'Po\\FlF R^MkM R^PkP R\\TlT Rc[g[ R^I^R RdCdQ RgRg[ R^IkIkQ R_VbY RYMRS RgDjD RWCSM R[WRY R[JXMRM RZRRS',
	    'Po]DlD R`GjG R]JlJ R^TlT Re[h[ RaJaQ RhQh[ RaVcY RVORS ReLhOlQ RWCSM RkLgN ReP`P RYWRX RYIXMRM RYRRS R]D\\Q[Y',
	    'Po]FkF R^LjL R^OjO R^SjS R\\VlV R^I^V R^IjIjV RhXk[ RUISM R[JYMRS RXCTK R`X[[ RdCbI RZRRT R[WRX RYLRM',
	    'Po`PjP RaSjS R`VkV Rb[l[ RfNfY RZN]N]XZ[ RVORS RdD`I R[E\\I RbGfKkM R]Xc[ RWDRM RYJVP RcFjFeK`M RYRRS RXLRM RZWRX',
	    'Po[FlF R[MlM R]QiQ R]I]Q R`T`[ R]IiIiQ ReUkZ RbDdF RjSfV RdQgW RWDRM RUOST RXIUQ RbQ^V[W RYSRS RYXRY RdZ^Z RWMRM',
	    'Po\\FlF R^MiM R`XhX Rh[k[ R\\P\\[ R^I^M R`S`X R`ShShX R^IiIiM R\\PkPk[ RWORS RcCdF RWDRM RZWRX RZRRS RYIXMRM',
	    'Po]FlF R`MjM R`QjQ R_UkU R^ZlZ R`H`Q ReHeZ R`HjHjQ RWMVPRS RdCeF RWDSM RYRRS RYWRX RYIXMRM R]F]S[Z',
	    'Po[FlF R]OjO Rg[k[ R\\R\\[ R]H]O RjHjO R\\RkRl[ RWORS RcCdF RgHcKgL RWCSM RcO_X R`HeJ`M RYIXMRM RYRRS RZWRX RgUhW_X',
	    'Po[JkJ RZ[l[ R\\R\\[ RaRa[ ReRe[ R\\RiRi[ RUQRT R`L[Q RgDfH R^C`H ReMkO RWCSM RXJUQ RYSRT RYXRY RXMRM',
	    'Po\\HlH R[OlO R\\SjS R`H`[ RfGf[ R\\KjKjS ReSlZ R^D`F R_UZZ RVPRS RWDRM RYIVP RgDeI R`R^W RYRRS RYVRX RXLRM',
	    'Po_KeK R_OjO R[UlU R\\G\\J R_K_U RfOfU R\\GkGkJ RcCdF RVORS RgXkZ RWCRM RYIVO R`XZ[ RYRRS RYWRY RXLRM RhJaJ',
	    'Po\\ElE R]MkM R^PjP R\\TlT Ra[d[ R]H]M RbEbM RdTd[ RfEfM R]HkHkM RhVlZ R`V\\Z RZJXNRS RWCSM R[WRY RXLRM RZRRS',
	    'Po^GjG R^JjJ R]QkQ R]M]Q R^D^J R^DjDjJ RWNRS RWCSM RaMaQ RfMfQ R^SbXfYl[ R]MkMkQ RZWRY R]TjTgWcY[[ RZIXMRM RZRRS',
	    'Po]HjH R]LjL RbTkT Ra[d[ R]D]L RdDdL R]TfTd[ R]DjDkL RhWkZ RhRlU R_W\\Z RWNRS RWCSM RbL^PeP\\T RYIXMRM RYRRS RZWRX',
	    'PoZTlT R[D[M R[DbDbM RdDdM RdDkDkM R_NbO RhNkQ RaQ_V RVORS RWCRM RYIVO R\\PZQ RePcP R^Wk[ RYWRY RXLRM RYRRS RgUdY[[ R^G^O RhFfO',
	    'PoaJlJ RaTkT RaZkZ R\\F[I R^L^[ RaOa[ RaOkOk[ R\\FlFlI RcCdF RWORS RWDTL R_I[Q RfJdO RXRRS RZWRX RYJXMRM',
	    'Po\\DbDbL RdDjDjM RVORS R\\G_G RcNlR ReGhH RWDRM RYIVP RaI[K RjIdK RdO[R RcR^T RfT^X RXRRS RYWRX RiWdY][ RXLRM',
	    'Po]NjN R]QjQ R\\TkT R]J]Q RdJd[ R]JjJjQ RdTjZ R]F_H RbFeI RgFjI RjCgF R_C]F RWNRS RdCbF RWCSM RdT`X[Z RZRRS RZIXMRM RZWRY',
	    'PocElE RZGbG RZMaM R[XaX R[Q[Y R^C^M R[QaQaX RdIdT RdIkIkT RiWl[ RWDRM RUOST RXITQ RhEfI ReZb[ RWSRS RXWRX RWLRM RgLgSfY',
	    'Po\\GkG R_PiP R_TiT R`[d[ R_K_S RdTd[ R]MiLiS RhVkZ R]I_K RfHkN RjIhK R_V[Z RWORS RaI\\N RWCRM RcC`K RYRRS RZWRX RYIWNRM',
	    'Po\\GkG R]JjJ R\\MlM R\\RlR R^ZjZ R^U^[ RdGdR R^UjUj[ R_C`F RVIRM RYMRS R_N_Q RiNgR RXCUJ RhCgG RZSRS R[XRY RYLRM',
	    'Po]OlO R^VkV R^ZkZ R]G]O R^R^[ ReGeO R^RkRk[ R]GlGlO R`CaF RiIhM RXNRS RiDfG R`IaM RWCSM RZRRS R[XRY RZIXMRM',
	    'Po\\ElE R^JjJ R^MjM R\\PlP R^UjU R^XjX R[[l[ R^G^M R^R^X RdRdX RdGdM R^GjGjM R^RjRjX RWORT RWDSM RZJXMRM R[SRT R[XRY',
	    'Po_IjI R^KlK R`QjQ R`XjX R`[l[ R_F_I R`N`W ReCeK R`NjNiQ R_FjFjI R`TjTjW RYM]M]X_Z R[E\\I R\\WY[ RWCRM RUOST RXIUQ RYRRS RYWRX RWLRM',
	    'Po^HhH R[PaP RePkP R[SlS R[K[P R^D^H R[KaKaP RcQc[ ReKeP R^DhDhH ReKkKkP RVPRS RcShXlZ RWCRM RYIVP RcS^XZZ RXSRS RYWRY RXLRM',
	    'Po\\IkI R[LlL R]SiS R\\D\\I R]N]S R_U_[ RaDaI RfDfI R]NjNjS R\\DkDkI RVORS RdUhYlZ RWCTL RYIWO RbT[W RkUfW RYRRS RYVRX ReZ^Z RXLRM',
	    'Po[JbJ R[NbN RZQcQ R[F[M R^C^F R[FbFbM RUPRS RgRjZ RiQeZ RWDRM RXIUP RfMiX RjMgX RXRRS RYWRX Ra[^[ ReJhV ReJkJhV R_O^Q\\Y R]UbUa[ RfCdO RWLRM',
	    'PodFkF R[FbF RiNlN R]P]V R_C_I RcIkIiN R]PjPjV RUPRS ReDdG ReXk[ RWDRM RXIUQ R\\DZG R]I\\M RhCgIdN RZIaIaM_M RZVRX RaYZ[ RcRbX RWLRM RYRRS',
	    'PoWHjH RRNlN RUYiY RURU[ R_H_Y RiRi[ RXCVISL',
	    'PoTG_G RRM`M RgWlW RSW^W RSPSZ RYGYW R^P^Y RcFc[ RcFlFlW RTCRK',
	    'Po_FkF RTH]H RRM]M RSX\\X R][l[ RSQS[ RXGXX R\\Q\\Y ReFe[ RTCSK',
	    'PoVGjG RRMlM RUUiU RUOUU R[X[[ R_F_U RiOiU RhWl[ RUXR[ RbWc[ RXCUJ',
	    'PoTG]G RRM]M R^PlP RSW\\W RSPSZ RXGXW R\\P\\Y R_HiHiP RgVl[ RbU\\[ RdCdPhY RTCRK RdLcU',
	    'Po^ElE RTH]H RRM]M RSW\\W Ri[l[ RSPSZ RXGXW R\\P\\Y RdOfU RlXl[ RTCSK RaMaR`U`Z RbEbMiKiZ][',
	    'PoTH]H RRM]M RSW\\W RSPSZ RXGXW R\\P\\Y RgVl[ RcV^[ RfHeRhY RePaX RaIkIjO RTCSK RbC_O ReLfU',
	    'PoTH\\H R^JkJ RRM]M RaVhV RSW\\W RSPSZ RXGXW R\\P\\Y RdCd[ RdHjU RdHcM^V RTCSK',
	    'PoTH]H R_IlI RRM]M R^QlQ RSW\\W RSPSZ RXGXW R\\P\\Y RiIi[ R`CbH RjChH RTCSK RbIbRaX',
	    'PoTH\\H R_KlK RRM]M RSW\\W RaZkZ RSPSZ RWGWW R\\P\\Y RaPa[ RaPkPk[ RTCSK R_E_R]Z RjD_E',
	    'PoTG[G RRN\\N Rg[l[ RTQTX RWGWX RgDg[ RlXl[ RfNkS RcO^T RaW][ R_GaL RkGhL RTCRK R[Q[XTX RcDcRaW',
	    'PoTG[G R`LjL RRN\\N RaViV Rg[l[ RTQTY RXGXX R^E^[ RaOaV ReLeV RiOiV R^ElEl[ RbGcJ RUCRJ RhFgL R\\Q[XTX',
	    'PoWQiQ RRUlU RUZiZ RTDTK RUWUZ RTD]D]K R_Q_Z RaDaK RiWi[ RaDjDjK RXOUR RgLlN RYL]N RWMRO RdM`N RYGWL RfGdL',
	    'PoWQiQ RRTlT RVZiZ RSLSO RVVV[ R_P_Z RiVi[ RSLkLkO RkEiG RUESG R]E\\G RbE`G RXOUS RXH]J ReHkJ RfDdI_J RXIRK RYCXI',
	    'PoSE_E RTH^H RhHlH RTN^N RWRiR RRUlU RVZiZ RVWV[ RYCYH RYJYM RTJ^J^M R_R_Z RbDaH RiWi[ RYPUS RbKdNjP R`JjJgMaP RbDhDhH RTJSQ',
	    'PodFkF RTG\\G RbKiK RRM\\M RdNkN R_VlV RSW[W Rb[e[ RSPSZ RWGWW R[P[Y ReCeN RaQfQf[ RbSbU R^HkHkK RiRhU RTCRK R^H^S]Z RjPcQ',
	    'PoTGZG R\\GlG R_KjK RRN[N R^PjP R^SjS R^WjW RSQSX RWGWX R^M^V R_D_J ReDdK R_DjDjJ R^MjMjV RgXlZ RUCSJ RbX\\[ R[Q[XSX',
	    'PoSG[G RRM[M ReNjN R^PjP R^SjS R\\UlU RSW[W Rc[g[ RSPSZ RWGWW R[P[Y R^K^S R`HgHfN RgSg[ R^KjKjS R_WbY RaEhElJ R_F\\J RTCSK RbHbN',
	    'Po]DlD RTG[G R]LkL RRN[N R^QjQ R^TjT R]WlW RSQSX RWGWX R]H]L R^N^T RbDbL RdTd[ RgDgL R^NjNkT R]HkHkL RUCSK R[Q[XSX',
	    'PoTH[H RRN[N R^TkT R]WlW RTQTY RWGWX R]G]K ReCeG R`IiIiM R]GlGlK RaEaG RjDgG RUCSK R^PfPe[a[ R`I`MjNbP R[QZYSY',
	    'PoRE^E RhIlI RSK]K RRM^M RVShS RRVlV RUZhZ RUWUZ RSG]G]K RTNTQ]O]P R_R_Z RbDaH RiWi[ RXCXQUT RbDhDhI RZI]I RSGTJWI RcMgOlP RXIUI R[IXI R`KjKfN_P',
	    'PoRElE R_IeJjK R_MjM R_PlP RVSiS RRUlU RUZiZ RUWU[ R_C_F R_R_Z R`H`P ReGeP RiWi[ RTHWK RWFUI RZHUM RaF\\J R\\KXPUT RRMZNTP',
	    'PoTG\\G RbGkG RTI\\I RbIkI RTL\\L RbLkL RWSiS RRUlU RVZiZ RTDTL RVWV[ RTD\\D\\L R_R_Z RbDbL RiWi[ RbDkDkL R[M]P RiMlP RcMaP RUMRP RYPUT',
	    'PoVFhF RVIhI RaMkM RSM]M RSP]P RaPkP RVSiS RRUlU RUZjZ RSKSP RUWU[ RVDVI RSK]K]P R_D_I R_R_Z RaKaP RfKfP RVDhDhI RjWj[ RaKkKkP RXKXPTT',
	    'Po_GjG RTH\\H R_IjI R^KkK RRM\\M R_PbP R_RbR RfRjR R_UjU RSW[W R]WlW RSPSZ RWGWW R[P[Y R_D_I RdKdS R_DjDjI R]N]OlNlP R][iZkZ RaW`[ RTCRK RfPiPfP',
	    'PocEjE RTG[G R`KfK RRM\\M RcMkM R`QjQ R`TjT RSW[W R^ZlZ RSPSZ RWGWW R[P[X R`V`Z R`O`T RdCdM RcVcZ ReOeT RgVgZ R`OjOjT R`VjVkZ R]HkHkJ RTCSK R]H]S\\Z',
	    'Po]ElE RSG[G RRM\\M R^McM RfMkM R_QkQ R_TkT R_WkW RSW[W R_ZlZ RSPSZ RWGWW R[P[Y R^I^M R`P`[ RbCbG R^IcIcM RfOfZ RfIfM RgCgG RfIkIkM R`N]T RTCSK',
	    'Pof[l[ RRER[ RRElEl[ RVK^S R_JhS RZOVX RdO_X R[EYT ReEbU',
	    'PoRWlW RRIR[ R[I[X RcIcX RRIlIl[',
	    'PoRIRZ RRIlIlZ R]PfY RWN`Q ReK[VVX',
	    'PoULiL RXPgP RYXfX Rf[l[ RRER[ RYPYW R_L_P RRElEl[ RYG[K RdGcJ',
	    'PobKjK RTNkN RRTlT R_N_[ RbEbK RSEkEkI RSESI R\\EZJSL',
	    'PoTKjK RTETK R[E[K RcEcK RTEjEjK R_McQlU R[VdZ R_MZQRU',
	    'PoSKlK RSDSK R[D[K RdDdK RSDlDlK R\\LYPSR RYPjOeU_XV[ R[R^S`XR[',
	    'PoTJjJ RSMkM RTDTJ R[D[J R`P`[ RcDcJ RTDjDjJ RbRlV RaM]RRW',
	    'PoSQlQ RTETI R_M_[ RTEjEjI R`QeVlY RbIjL R[HYJSM R^QYVRY',
	    'PoSJlJ Re[k[ RSESJ RRQXQX[ R[E[J RcMcV RdEdJ RkLk[ RSElElJ RVL[N R^XWZ',
	    'PoTJjJ R^YeY R[YaY RWZ_Z RTDTI R[D[I RVM^M^Y RcDcI RTDjDjI RcTfZ RgSkZ RfLiW RVMVRUX RhK^M',
	    'PoTJjJ RSOkO RTUjU RR[l[ RTETJ R[E[J R_Q_[ RcEcJ RTEjEjJ R_L_P',
	    'PoTJjJ RWRgR RWVgV RR[l[ RTETJ RWMW[ R[E[J RcEcJ RWMgMg[ RTEjEjJ',
	    'PoSIkI RULhL R[[`[ RSDSI RaPa[ RSDkDkI RRQlQgU R[NcQ RgL_P R_Q[URY R[E[I RcEcI',
	    'PoTJjJ RROlO RVZhZ RTDTJ RVSV[ R[D[J R_J_S RcDcJ RVShSh[ RTDjDjJ',
	    'PoSJjJ RTQiQ RTUlU RT[\\Y RSDSI RTMT[ R[D[I RbDbI RTMiMiQ RSDjDjI R_QaWdZk\\lY',
	    'PoTJjJ RSOkO R_TjT RR[l[ RTETJ RWRW[ R[E[J R_O_[ RcEcJ RTEjEjJ',
	    'PoSKkK RTOjO RRTlT R`ZiZ R^ZfZ R[ZdZ RUZ_Z RSDSK R[D[K R_L_T RcDcK RSDkDkK ReUk[ RYWU[ R\\TWY',
	    'PoTJjJ RUNiN RSRkR RTVjV RR[l[ RTETJ R[E[J R_K_R R_T_[ RcEcJ RTEjEjJ',
	    'PoTIlI RVQjQ RfYkY R`M`[ RTMjMjQ RZJ]M ReJbM RUUlUkY RTETI R[E\\I RdEdI RTDlElI RUQTV R`TXXRZ',
	    'PoRHlH RUNiN RSTkT RSWkW Re[j[ RSQS[ RUKUN RUKiKiN RSQjQj[ RZD[H RcDcH RRDlDlI RRDRI',
	    'PoTIjI R]KcK RRUlU RZ[_[ RTDTI R[D[I R_S_[ RcDcI RTDjDjI RWL_L`N RhLkO RWKRP RWQgQ_S RgJ`K',
	    'PoTJjJ RRO]O R_OlO RTDTJ RXKX[ R[D[J RcDcJ ReKe[ RTDjDjJ RXQ\\U RhTlX RbS]X RVTRX RfOiV RXNUV RdOaU ReNcR',
	    'PoTIjI RRLlL RWThT RWXhX R_[k[ RTDTI RWOWY R`N`[ RUPhPhW RTDjDjI RkXk[ RdLhPlR R\\JWPRR R[E[I RcEcI',
	    'PoTIjI R_KkK RUQiQ RUUiU RRXlX RTDTI RUNUT R_I_N R_U_[ RUNiNiT RTDjDjI R[E[I RcEcI',
	    'PoTJkJ RbNkN RTR\\R RbRkR RbVlV RTETJ R[E[J RbLb[ RcEcJ RTEkEkJ R[URW R\\L\\VYYU[ RUN\\N',
	    'PoTIjI RSM`M RRQaQ RSU`U RTETI RYJYQ R[E[I RbEbI RdKd[ RTEjEjI RhRkU ReQjS RYRYYRZ RaY\\YVZ',
	    'PoSIjI RSDSI RZDZI RbDbI RSDjDjI RaMjQ RUM^Q RaTeXk[ RUV^Z RSL]LZOSR R`LjLgO`R RST]TZXS[ R`TjTgX`[',
	    'PoTIjI RRMlM RTU]U RTDTI RTPTU R[D[I RTP]P]U RcDcI RTDjDjI RdVh[ RaPeY RdJhL RhRbX][ R`LcW RlXk[i[ R_XRY',
	    'PoSIjI RRKkK RYQhQ RYThT RYWhW RTZlZ RSDSH RTOT[ RYNYW R[D[H RbDbH RYNhNhW RSDjDjH R_I_O',
	    'PoTIjI RTMjM RRQlQ RWWgW RWZgZ RTDTI RWTW[ R[D[I RbEcI RWTgTh[ RTDjDjI RYNZP ReNbQ',
	    'PoTIlI RTL^L RRNaN RTQ^Q RTS^S RTZ^Z Rf[l[ RTDTI RTVT[ R[E\\I RTV^V^Z RdEdI RdLdW RTDlDlI RlKl[',
	    'PoTIjI RRLlL RXSfS RWWgW RTDTI RTOT[ R[D[I R_J^O R_S_[ RcDcI RTDjDjI RZP]S RdPaS RTOjOj[f[',
	    'PoTIjI RULfL RRPlP RZWiW RZZiZ RTDTI RZSZ[ R^J^P RZTiTi[ RTDjDjI R[E[I RcEcI RiL^QRU',
	    'PoTIjI RVOhO RVShS RZ[e[ RTETI RVKVS R[U[[ R[E[I R_K_S RcEcI RVKhKhS RTEjEkI RgUlZ RUWR[ ReXe[ R]UaV',
	    'PoTIkI RULiL RRPlP RTDTI R\\W\\Z R_J_P RTDkDkI RhWk[ RbWcZ RVU^UaUgSiU RVWR[ R\\RUT R[E[I RcEcI',
	    'PoTHlH RTDTH RZWZ[ R\\D\\H R`K`T RdDdH RTDlDlH RdVgY R_W`Z RUXR[ RWKWTmTk[g[ RWKlK RWQiQ RWNiN',
	    'PoRIlI RfQjQ RUVjV RUZjZ RRDRI RUSU[ R_S_Z RUSjSj[ RRDlDlI RZM]P ReMaQ RSLRQ R[E[I RcEcI R`KlKjQ R\\ORP R[KRL',
	    'PoTIkI RRO_O RbQkQ RTT]T RTW]W Rb[l[ RY[][ RTDTI RTQT[ RTQ]Q][ RbJbQ RbSbZ RTDkDkI RkNkQ R\\L_O RlXl[ RXJTN R[E[I RcEcI RjLaM RkTaV',
	    'PoTIjI RTL]L RRN^N R`OlO RTQ]Q RTS]S RTZ]Z Rc[h[ RTDTI RTVT[ R[E[I RTV]V]Z RbEcI RhKh[ RTDjDjI RbRcV',
	    'PoTIjI R^NlN R^RkR R^VkV R^ZlZ RTDTI RWJW[ R[D[I R_O_Z RcDcI ReMeZ RTDjDkI RZL[O RfJdN RTMRS R`K^O[R',
	    'PoTHjH RUQiQ RUTiT RRVlV RTDTH RUNUS R[D[H R_N_[ RcDcH RUNiNiS RTDjDjH RVKYL R]K`L RXIUJ R`I]J RgJeKhL R_VeYl[ R_VZYR[',
	    'PoTIjI RTP_P RaPlP RWR_R RUU`U RX[Z[ Rd[h[ RTDTI R[U[[ R[D[I RTL_L_O RcDcI RhKh[ RTDjDjI RcRdV R^W_Z RWXUZ RTLTTSZ',
	    'PoTHjH RUMiM RUPiP RRSlS RSVlV RSXkX RTDTH RUJUP RXQXU R[D[H R_J_[ RcDcH RfQfU RUJiJiP RTDjDjH',
	    'PoUHkH RTKlK Rg[j[ RUDUH R\\D\\H RcDcH ReOeW RjMj[ RUDkDkH RXUWX RaN_P RaU_W RXNWQ R\\QaS R\\XbZ R[RWS R[YV[ R\\M[R R\\T[Y RTKTTSZ',
	    'PoTHjH RVRhR RWWgW RWZgZ RTDTH RVMVR RWUW[ R_M_R RWUgUg[ RTNiMhR RTDjDjH RYO[Q RdObQ R\\KdKlN RYKRN R[D[H RbDcH',
	    'PoTHjH RTJjJ RSNlN RVShS RVVhV RTXjX RR[l[ RTDTH RVPVV R[D[H R_H_J R_P_[ RbDcH RVPhPhV RTDjDjH ReJcN RZJZN',
	    'PoWF]H RRJlJ RTM\\M RbQjQ RUWiW RUZiZ RUDUI RUTU[ R\\J\\M RTQ\\Q\\T R_T_Z RbQbT RUTiTi[ RUDiDiI RbJbMjMjQ RfF`H R\\FWH RaFgH RTMTQ',
	    'PoTHkH RRL_L RUO\\O RST^T R`ViV RRW^W Re[j[ RSQTT RTDTH RUJUN RXOX[ R\\J\\N RSQ^Q^T RTDkDkH R[D\\H RcDcH RaKjKhR RbNaRlRj[',
	    'PoTIkI R`OkO RRS]S R_TlT R_WlW Rh[l[ RTDTI RXSX[ R\\D\\I R_Q_[ R`K`O RdDdI R`KkKkO RTDkDlI R_QlQl[ R[Q]S RTMWQ R[U]Y RXJTN R[MTS RTURZ',
	    'PoTHjH RRJkJ RVOhO RVQhQ RRTlT RdZgZ RTDTH RVMVQ RWUWZ R[D[H R_T_[ RbDbH RVMhMhQ RTDjDjH RdTkW R\\QYTRW RUVhVgZ',
	    'PoTIjI ReLkL R_RkR RST[T R_UkU R^XlX RTDTI RWVXZ R_N_U ReJeN ReUe[ RTDjDjI R_NkNkU RWJSN R[LST R[U\\Y R[Q\\U RTVRZ R[E[I RbEcI RYNRO',
	    'PoTIjI R_OlO R`RkR RTS\\S R`VkV RaZmZ RTDTI RXSX[ R[D[I RaOaZ RcDcI RfOfZ RTDjDjI R[P]T RTMWQ RWKTN R[LUS R\\U\\Y RTURZ RaJ_P RgKeO',
	    'PoTHjH RRM^M RTQ]Q RTT]T RTOTW RTDTH R[D[H R\\X\\[ RcDcH RTDjDjH RbJbOkNkO R[J^M RiXl[ RaXc[ RVXS[ RWJTL RbPbVlUlV RjJaK RTO]O]VZV RjQaS',
	    'PoTHjH RRL^L R`LkL RUO[O RSU]U RRX^X RaXfX Re[i[ RSQSU RUJUO RXOX[ R[J[O RSQ]Q]U RaTaY RaTfTfX R_QkQj[ RTDjEjH ReMkO RfLcO`P RTETH R[E[H RcEcH',
	    'PoTHkH RaKlK RRL_L RaNkN RUO\\O RaQkQ RSU^U RRX_X Rg[k[ RSQSU RUJUO RXOX[ R\\J\\O RSQ^Q^U RcDcH RcWcZ RfKfT ReWfY RhViW RaW_[ R[E[I RTETI RaKaUlTk[ RTDkEkI',
	    'PoTHjH R[McM R\\PbP R\\SbS R\\ZbZ RSWR[ RVWVZ R\\V\\Z R\\VbVbZ RTDjEjI RkMdS RXNRT RYVYY R_J_L ReVdZ RUJSO RgJeO R[E[I RcEcI RTETI RhSh[e[ RkVkZ RXRYTRT RjQkSdT RWORO RiOdO',
	    'PoTHjH RUNiN RRTlT R_H_[ RXCYG RfCdG',
	    'PoRGYG ReGlG RUNjN RRTlT RYCYJ R_C_J R_K_[ ReCeJ',
	    'PoTHjH RULiL RRPlP Rb[k[ R_H_P RcPcZ RlWk[ RXC[F RfCcG RYVVYR[ RZQXX',
	    'PoSGkG RVNhN RWRgR RSVkV R_N_[ R[K\\N RcJbM RbGfLlN R]C\\GWLRN',
	    'PoTHjH RUKiK RROlO RRTlT RXDZF RfDcF R_TcXfZl[ R_H_P^U[YS[',
	    'PoRI^I RSN^N RWWS[ RTDUG R\\C[G RiDfG`J RjKgOaQ RlShW^[ RXIXRWW R^RRT',
	    'PocFlF RUJiJ RRNlN RRF[D[H R_G_N RcCcH RbRcVfYl[ R\\NVTRV RXSbR]WYYR[',
	    'PoSGjG RTKiK RRNkN R_G_N RXCZG RVTRU ReCcF R[NTU RbRcVfYl[ RXRaR]WXYR[',
	    'PoRI\\I RSN\\N RRS\\S Rc[h[ RfFjN RbF^M R[CXIWTTZRZ RTCUG RbO`X R^NiNiTh[ RWPVX',
	    'PoRI\\I RSN[N R^PkP RRS\\S R^[k[ R^E^Z RdEdP R^EkEkR RVWR[ RTCUG RlWkZ R[CXIWRVX',
	    'PoSHkH RUMiM RRSlS R[V\\[ R_H_S RhVkY RXDZF RbVc[ RUVS[ ReCcH',
	    'PoSFkF RUJiJ RRMlM RZ[_[ R_F_M RVR_Q_[ RYCZF ReCbF R_RdXk[ RST[TWXRZ RiSbV RZNcP',
	    'PoRI\\I RhLkL RSN\\N R`DhDhL RkIkL RVWS[ RTDUG R`QaTeXl[ R[CZG R^PiPgScX[[ R`D_K RXIWRVX R\\SRT',
	    'PoTHjH RULiL RRPlP Ra[k[ R_H_O RaPaZ RlWkZ RfDcG RYDZH RYVWYR[ R[PYX RdRcW RhTiWbW',
	    'Po]IlI RTK[K R^NkN RTR[R R\\TlT RW[[[ RTD[D[[ RdId[ R_CaH RiChH RTDTSS[',
	    'PoSGkG RUKjK RRNlN R_G_N RXCZF ReDbG RXRTV RhRdU R_QaVcXgZl[ R_Q]V[XXZR[',
	    'PoSI\\I R`LhL RSN[N RfXjX RbPb[ R^PjPjX RdDlK RdD\\L RTCUG R[CZG R[SRT RXIXRVXS[',
	    'PoSFkF RUJiJ RRMlM RUSiS RfZjZ R_F_L RbNb[ RTPiPiS RYC[F RUSTVjVjZ ReCcF R\\N[VXYT[',
	    'PoSI\\I RSN\\N R^NlN Ra[e[ R^G^K ReCeG ReNe[ R^GlGlK RVWS[ RUDUH R[CZG RXIWRVX RZSRT R\\SUS',
	    'PoSI\\I R_MlM RSN\\N R[ZfZ R_E_V RVWS[ RTDUG RlVkZ R[CYG RXIWTUZ RdU\\W RfEfNhX RWPVX RjD_E R[SRT',
	    'PoTGkG RUKiK RRNlN RYVlV RV[l[ R_G_N RZRgRg[ RXDZF RfDcF R_R^[ RZOVWR[',
	    'PoRI\\I RSM[M R_QkQ RRS\\S R]ZlZ ReQeZ RTDUG ReKlN R_EjEdK^N RZCWIWQVX',
	    'Po\\FlF RRI[I R^NkN RSN[N RRS[S R^JkJkN ReUjZ RbU]Z RdCdRfX RTZRZ RjWhV R^N]RkRkW RTDTH RZCXIWSVX RdMcU',
	    'PoSFkF RUIiI RRMlM RSQZQ R_F_M RYC[F ReCcF RcUfYl[ R^QjQiU R^N\\T RbU_YY[ RZVRX RcQcW',
	    'Po]HlH RRI\\I RSN[N R\\PdP RgDjE RgT`[ RTCTG RjLeV RlWk[ RZCYG RTZRZ RfCfMfPhZ RWIWRVX R`K`Q_V R[RRS',
	    'PoRGlG RVJgJ RYOiO RTRjR RUUiU RRXlX R_R_[ RVEgEgJ RXLiLiO RXP[R ReOdR R]E\\IYLSN RYKYO',
	    'PoaIkI RRI_I RTN]N RaNkN R`TlT RVZ^Z RTE]E]N RfIf[ RbCcG RjChG RXEWNRU RUS^S^ZUZUS',
	    'Po^ElE RRI[I RSN[N R^SkS RRS[S R\\[l[ ReSeZ RdLeQ RiMjQ RjGhL RTDTH R^K`Q RZCXIWTTZ R`G^L ReGcL RWPVX',
	    'PoRI\\I R`KiK RSN[N R]PlP Rf[l[ RTCTG R^G^J RdCdG RgPg[ R^GkGkJ RlXl[ RVWR[ R`W][ R[CYG RbP`X RWIWSVX R\\RRT',
	    'PoRI\\I R_IjI RSN[N RRS\\S RgKkO RfEkJ RVWR[ RcK^P RbP]U RdC_I RTDUG RaSfXl[ RaRiRdX\\[ R[CXIWRVX',
	    'PoSFkF RUIiI RRMlM R_F_M RYC[F RXVS[ ReCcF RTOYP RRRWS R]QjQiU R^N[T RcQcV_YY[ RcVfYl[',
	    'PoSFkF RUJiJ RRMlM RRSlS R_F_M RYCZF ReDcF RfOjP RhUgW][ R]OTP RSW\\V RaOcWfZk[mY RYOY[U[',
	    'PoRI\\I R_KjK RSN\\N R_PjP R_G_Z RdCdG R_GjGjP ReUk[ RVWS[ RTDUG RcPgX R[CZG RXIXTUZ RkSfU RfY\\Z RWOVX R[SST',
	    'PoRI\\I RbJgJ RSN\\N R^RkR R]ZlZ R]G]J RdCdG RdRdZ R]FlFlI RhKlO RVWR[ R`K^O RTDUG RZCZG RWIWTTZ R\\RRT RWOVX',
	    'Po]HlH RRIZI R_LdL RRNZN RRSZS R_UdU R_O_W R_OdOdU RlWk[ RhDkF RgUa[ RTDTH RjMeX RTZR[ RgCgLgOhZ RYCWIVRUX R]H]R[Z',
	    'PoRI[I R`KhK RSN[N R`[c[ R]N][ R]NcNc[ R^UaV R_EdDkK RVWR[ RcE\\L RdTg[ RhTk[ R_QaR RfNeU RkNhT RZCYG RTDUG R[RRT RWIWSVX',
	    'PoTFjF RVIhI RRKlK RUQiQ RUSiS RRVlV R_F_K RbMbO RYC\\F RUMSO ReCbF R[M[O RgMkN R_Q^VbYl[ R^V[YR[',
	    'Po_HkH RRI\\I R_LkL RSN\\N R_XiX Rg[k[ R_R_Y R_D_L R_DkDkL RdTgU RVWR[ RdRaV RTCUG R[CZG RaL`P\\SRT R`PlPlVk[ RXIWRVX',
	    'Po]IjI RRI[I R\\LlL RSNZN RRS[S R]D]H RdCdH RjDjH RgWk[ RdW`[ RTDUG RfRhY RjPiU RZCWIWTTZ RbPaU RWPVX R`K^W RfNeW',
	    'Po_ElE RRI\\I RbMjM RRN[N RbQjQ RRS\\S Rc[f[ RbIbQ RfQf[ RbIjIjQ RVWR[ RTDTG RiTlY RbT`Y RgEeI R[CXIWRVX R_E_S][',
	    'PoSFkF RUIiI RRLlL RRQlQ RTU^U RX[\\[ RWUVX]W\\[ R_F_L RdWh[ RYC[F ReCcF RbReY RfNiO RSN[NYR`S RYPVSRT RiSdX_Z RlXk[i[ RbNcW',
	    'PoRI[I R\\JlJ RSN[N RRS[S R^VjV R^ZjZ R^Q^[ RdDdP RdQdZ R^QjQj[ R_F`I RdIkP RTCUG RiFgJ RdJ_O[P RTZR[ RYCXHWSVX RiC^E',
	    'PoUFiF RVIhI RTLkL RSQ^Q R`QlQ RST]T RaTkT RRW^W R`WlW RXQX[ R_F_O RfQf[ RUNVQ RcNdQ RYD\\F RiNhQ RdDbF R[NZQ',
	    'Po]FkF RRI[I R]JlJ RSN[N R^PjP RRS[S R^TjT R^WjW R^M^W ReCeH RhHhM RaH_MjMjW RTDTG RgXl[ RTZRZ RbX[[ RZCXIWSVX',
	    'Po]FlF RRI\\I RbMgM RSN\\N R^PjP R_UiU R_XiX R[[l[ R^H^O R_R_X RbJbM RdCdF RbJgJgM R_RjRjX R^HjHjO RVWR[ RTDUG R[CZG RWIWTTZ R\\RRT RWOVX',
	    'PoRI[I RRN[N R]SbS ReSjS RRS[S R]M]S R]MbMbS ReMeS ReMjMjS RhWl[ R_VbY RdDhH RVWR[ RdC^I RTDTG R`JgIlI RZCXIWTTZ R_U]Z RgTeZc[ RWPVX',
	    'PoTEkE RUHjH RVMiM R[QaQ RcTgT R[TaT RTVYV R[WbW Ri[l[ RU[X[ RTETH RVJUM RTPYPY[ R^Q^[ RVJhJhM RbPiPj[ RlXl[ R\\O\\R R_O`R RTPTVSZ RePeUdY RXSTS',
	    'PoTFjF RUHiH RRKkK RTOjO RURiR RRTlT RRWkW R_F_K RXMZO RXCZF ReMcO RULSN ReCcF RbLbN RgLlN R[L[N R_O_V[YS[ RbZl[',
	    'Po]EkE RRI[I R^JbJ RfJjJ R^MbM RfMjM RRNZN R\\PlP RRS[S R\\H\\K R]T][ RcSc[ RdEdN RgTg[ R\\HlHlK RTZRZ R]TlTl[i[ RTDTH RcPbW RYCWIWSVX',
	    'Po]ElE RSIZI R]KkK RSNZN RaRhR RSS[S RaOaR RbFbK RfFfK R]HkHkK RcOfP RiPkR R_P^R RbS^W RTDUG RaWj[ R]NkNkP RZCWIWTSZ R`UiUfX_[ RWNUX R]E]R[Y',
	    'PoUGjG RYKiK RZMhM RXOkO RWS`S RcSlS RWU_U RcUkU RVX`X RbXlX R[S[[ RaKaQ RgSg[ RUDjDjH RYQZS RdQeS R\\I^J ReHcJ R^Q\\S RiQgT RUDUQSY',
	    'PoX[^[ Rf[l[ RRE^E^[ RaElEl[ RTIXN RbIfM R^QRV RlP`U',
	    'PoaElE R`ZlZ RT[W[ RSEXEX[ RgEgY R^PZU RWPRU RTIUN RZI\\N RZE^E^[[[',
	    'PoRTlT RSE]E]O ReQe[ R`EjEjO R[HXLRN ReG_I RiHfK`N RYVWYR[ RXFSI RZQYX',
	    'PobRlR RT[X[ R[[_[ RREXEX[ RYE_E_[ RbIbS RgCg[ RbIlIlS RVHRL R]HYL RVQRT R]O[T',
	    'PoULgL RY[][ Rf[j[ RSO]O][ R`OjOj[ RbReS RURXS ReJhM R]F\\JYL RbIfK RdCgGlI RZCXFRH RZVRX RgV_Y',
	    'Poe[h[ R^PYT RWPRU RTIUN RZI[M RgFkN RcG`O RcEaM RSEWEW[T[ RYE^E^[[[ RdOcX RaOjOiUh[',
	    'PoSH_H Ra[d[ Rh[l[ R_EdEd[ RfElEl[ RkHgL RbR_U RcI_L RjNhT RcOaT RWLSY RZLWY RXCUU R]S^YVY',
	    'Po`HlH RT[W[ RSEXEX[ RfCfN RdTj[ R^PYU RWPRU RTIUN RZI\\M RbNfW R`NkNgUcY`[ RYE^E^[[[',
	    'PoRH\\H R^WbW R]ZlZ RWCWM R]EbEbW RYUR[ RaN\\S RiGeK RiOdS RaG]K RTNVV ReEjEjWfW RRN[NVVYX\\ZcZ',
	    'PoRVlV RSE]E]K R_S_[ R`EjEjL RTNeNeSkRkS RfF`G RiH`L RZFSH R\\ISL R]NZRST',
	    'PoRQlQ RYTgT RSD]D]L R`DjDjL RZSSZ RcNhO R\\MXV RaGgH R]ISK RjIaK RfU`YW[ RUGZH RZS\\VaYdZl[',
	    'PoRK\\K RRQ\\Q R`[d[ RRERS RWEW[ RRE\\E\\S R^EdEd[ RbP^T RkHfL RjQfT RbH^L RfElEl[h[',
	    'PoRH\\H RTU\\U R`[d[ Rh[l[ RXCX[ R^EdEd[ RfElEl[ RbR^U RdP`T RlOgT RZN\\Q R_IaN RgIiM RXHST',
	    'PoRH[H R^[c[ Rh[l[ RWCWH R\\DcDc[ RfDlDl[ RaQ[W RjHeM RiReW RaH\\M RjOhU RTKUS RYKXU R\\TRV',
	    'PoaIlI RaZlZ R[[^[ RT[W[ RSEXEX[ RYE^E^[ R^PYU RWPRU RTIUN RZI\\N RfCfG RcLeW RjLgZ',
	    'PoYP]P RfPjP RUSiS RR[l[ RSE]E]O R_P_S R`EjEjO RUGXJ RbGeJ RYT[Z ReTcZ RhK`N RZLRN',
	    'PoT[W[ R[[^[ RSEWEX[ RYE^E^[ RfCfN ReUk[ R^PYU RhTb[ RWPRU RTIUN RZI\\M RcNgW RaHkHkL RaOkOgW RaHaR`Z',
	    'PoUJ[J RYW\\W R`[d[ Rh[l[ RWNW[ RSN\\N\\W R^EdEd[ RfElEl[ RXC]I RkHfM RbQ^U RjOiRfU RWERJ RcH^M',
	    'Po`DjDjM R`MkR RfE`H RTD]D]L\\NRS RgIbK R_PWT RYETH R[HYJSL RaTWW RgUaXY[ RbXV[',
	    'PoaIkI R`PkP RT[W[ R[[^[ RSEXEX[ RYE^E^[ RhWl[ R^PYU RWPRU RTIUN RZI\\N RfCfRjZ ReSbZ RbD`L RfMfQdW',
	    'PobThT RU[X[ R[[^[ Re[j[ RSEXEX[ RZE^E_[ RbMbV RbMhMhT R^PZU RXPRU RTIUN RZI\\M RcCaL RbHlHlTk[',
	    'PoYN]N RhNlN RWUiU RWZiZ RWQW[ RSE^E^N RWQiQi[ RaElElN R`N]Q RUGYH RbGfH RZKRM RiJaM',
	    'PoRH]H RSN\\N RSZ\\Z Ri[l[ RSQS[ RWCWN RSQ\\Q[[ R^DdDd[ RfDlDl[ RdP`T RlOgT R`IaM RgIiM Ra[d[a[',
	    'PoRI\\I RSN\\N R_[d[ Rg[l[ R^EdEd[ RfElEl[ RVWR[ RTDUG R_JaN RgJiN R[CZG RbS\\U RjSeU R\\RRT RXIXRVX',
	    'PoWOgO RY[][ RSQ]Q][ RWKgKgN R_CjH RYSTU R[TXXSZ RfR`U RhTfW`Z R^DSI RZHcGlI RWKWO R`QjQj[f[',
	    'PoTL[L RTX\\X R`[d[ Rh[l[ RTPTZ RTP\\P\\X R^EdEd[ RfElEl[ RXC\\H RkHfM RbQ]U RjQfU RVGRK RcH^M RWCUI',
	    'PoVG\\G Ra[e[ Rh[l[ R_EeEe[ RfElEl[ RUJYK RWTYW RbR_U RePaS RlPgT RXDSI RYNST RaIbN RhIjN R\\HWMRO RWP^P\\UXYR[',
	    'PoRR^R R[[k[ R[R[Z R^M^P R_EdEdW RdNaP_S RjMhPfR RfEjEkWkZ R`GbL RfGhL RWGZN]O R\\HZKRO RTZR[ RWRUY R`WdW`W RgWjWgW RWDYM R]FRH',
	    'PoTK^K Rb[f[ Ri[l[ RTETJ RYCYJ R^E^J RaEfEf[ RhElEl[ RfPaU RlPgT RWNRS RbIcN RhIjN RWLUQ RVP]OZW RVT[UWYR[',
	    'PoTOiO RVTiT RRWlW RRLRO RRD]D]J R`Q`[ R`DjDjJ RRLkLkO R\\NVT RTFYG RaFfG RjG`J R]HRJ',
	    'Po_GlG R_[c[ Rg[k[ RWHW[ RZGZX R\\OcOc[ ReOkOk[ R`P]S RaU]Y RhPeT RiTeX RaD\\I RXDTM R`FcJfLlM RiFfJbL\\M',
	    'PoRNlN R`DjDjL RWQ[U RgRjU RfQcT RWQSU RUGYH RTD]D^L_QaUdXk[ RbGgG R^T[XT[ R]ISL RjI`K R_N^U',
	    'PoSM^M Rb[e[ Ri[l[ RTGTK RXCYG RTG]G^J R`EeEeZ RgElEl[ ReO_U RlOgT RaIbM RhIiM RWMSY RYOVZ RWJUW R\\T]ZVZ',
	    'Po`GlG RaQjQ RZ[][ Rb[e[ RYE]E][ RaKaQ RfQf[ RaKjKjQ R]QYT RWQRU RSITN RYI[M RiTlY ReCfG RbT`Y RREWEW[T[',
	    'PoVQjQ RVSjS RVWjW RVZlZ RWOW[ RSD]D]M RaOaZ RaDkDkM RYMRS RfE`H RhH`L RYFSH RZISL',
	    'PoZL]L RgLjL RTOjO RRWlW RSE]E^L_O R_U_[ R`EjEjL RUFXH RYR^S ReP`T RZPVT RcRiT RbGeH RZJRK RgJ`K',
	    'PobFkF RTI\\I RbIjI RbLlL RY[][ Rf[j[ RSF\\D\\K RSQ]Q][ RbCbO R`QjQj[ RRL[LUO RYRST R[TXXSZ ReR`T RiTeX_Z',
	    'PoVI[I RVM\\M RVV[V RZ[^[ Rb[e[ Ri[l[ RVPVW RYGYM RVP[P[V RTE^E^[ RaEfEf[ RgElEl[ ReP`U RlPgT RaIcM RhIjM RTETTR[',
	    'PoTMjM RRQlQ RRTlT RTD]D]J RaDjDjJ RZN[Q RdNcQ RfD`G RhGaK RZDTG R\\GTK R]QWWk[ RfTaYS[',
	    'PoSJ]J RRS^S Ra[e[ Rh[l[ RRMR[ RSDSJ RVMV[ RZMZ[ RSD]D]J R`EeEe[ RgElEl[ RkHfL RdH`L RjNhT RdOaT ReMcR RRM^M^[\\[',
	    'PoRHlH RYOhO RY[][ Rf[j[ RYJYO RSQ]Q][ R^C^H RUK`LhLhO R`QjQj[ RiD^I RYSSU R[UXXSZ RfR`U RiTfW`Z RbIhK RVEeFSL',
	    'PoRFlF RY[][ Rf[j[ RUHUO RSQ]Q][ RbHbN RiHiO R`QjQj[ RZDZF ReCcF RWJ[J RWLZM RYRSU R[UXXSZ RfR`U RiTfW`Z RUH]H]OZO RePiPeO',
	    'PoTJ]J RTR]R RTV]V Ri[l[ RRFRI RTMTV RTM]M]V RRF_F_I RgElEl[ RXDZF ReP`U RlPgT RbIcN RhIjM RaEeEe[b[ R^YUZ',
	    'PoWNhN RWQhQ RSSkS R_WiW R^[l[ RWLWQ RTD]D]J R_S_Z RWLhLhQ R`DjDjJ RXVS[ ReE`G RiGaJ RYESG R\\GTK RWW\\ZdZ',
	    'PoSE_E RTM^M RUU_U Rb[e[ Rh[l[ RTHUSU[ RXQX[ R[Q[[ RTH^H^M RaEeEe[ RgElEl[ RdI`M RjIgM RjOhU RcOaU RTORY RUQ_Q_[][',
	    'Po`LkL RbSjS R[[][ RYE]E][ R`E`L RkEkL RhMkQ RcEhJ R]PYU RcM_Q RWPRU RhDcJ RTIUN RZI[M RdPaTgXl[ RiTeY_[ RSEWEW[T[',
	    'PobEkE RaMlM RTU^U RTY^Y Rh[l[ RTQT[ RTQ^Q^[ RhMh[ RlWlZ RUFSH RUJRM R\\FZH R]JZL RSDXDXOYQ RUNXNUN RZD_D_N\\N RdNbY',
	    'PoUNiN RVThT RVVhV RRYlY RRLRO RVQVV RSD]D]J R_M_[ RVQhQhV RaDjDjJ RRLkLkO RfD`G RhG`J RZDTG R\\GTJ',
	    'PobJlJ RcOkO RbSlS RS[V[ RREVEV[ R`G`[ RdDjEiJ RhWl[ R[QXT RfWb[ RVQRU RSITN RXIZM RgNgTiY RaD]L RdKbQ RXE\\E\\[Y[ RfPfX',
	    'PoRE^E RTM]M RZS^S RUV\\V Rh[l[ R[[^[ RSPS[ RTHTL RXVXZ RZPZS RTH]H]L RSP^P^[ RgElEl[ RjIgM RcQ`U RjQfT RdI`M RWPUT R`EeEe[a[',
	    'PoRMlM RWRhR RYZeZ Rg[k[ RSTS[ RWOWR RYWY[ RRD]D]K RYWeWeZ RWOhOhR R`DkDkK RSTkTk[ RfE`G RiGaK RZHRJ RYFRG',
	    'PoRG]G RSN\\N RSR\\R RRV]V R`[c[ Rg[j[ RSJSR RXCXJ RXRX[ RSJ\\J\\R R^LcLc[ ReLjLj[ ReClJ RaN]R R`U\\Y RhOeR ReC]J RhSeX',
	    'PoTI^I RTM^M RSP_P RTW]W Ra[e[ Rh[l[ RTFTM RYSY[ RTF^F^M R`EeEe[ RgElEl[ RZP^U ReP`U RlOgT RXCWF RWPRU RaIbN RhIjM RWNUT',
	    'PoSE_E RSM_M RTP^P RRS_S Rb[f[ Rh[l[ RV[Y[ RSHSM RWEWM RYSY[ R[E[M RSH_H_M RaEfEf[ RgElEl[ R\\V_Y RjIgL RUVRY RkNhT RlMjQ RdGbL RdObU RfMcS',
	    'PoRD^D RUH]H RgHkH RTJ]J RRN^N RX[\\[ Rf[j[ RRDRN RRQ\\Q\\[ R_QjQj[ RaDgDgH RXK]M RbD`H RYGWLTM RXRRU R`JiJgMaO RZUXXRZ ReR`U RhTfW_Z RdMlO',
	    'PobFkF RcMkM RcQkQ RbUlU Rd[l[ RY[\\[ RXE\\E\\[ RcJcQ RgCgY RcJkJkQ R^E_H R^M`M`X][ RVPRT R\\QXT RSITN RXIZM R`Xe[ RREVEV[S[',
	    'PoaIjI RaMjM RaPlP R[[^[ RT[W[ Rf[j[ RSEXEX[ RYE^E^[ RcVcY RaFjFjL RhUjW R^PYU RWPRU ReCeF RTIUN RZI[M ReUfX R`U`Z RaFaRkSj[',
	    'PoSJ]J RSN]N RSPVQ RRW_W Ra[e[ Rh[l[ RSFSN RXCXF RYOY[ RSF]F]N R`EeEe[ RgElEl[ RePaS RlPhS RaIbM RhIiM R[T_T RVTRT R^P[Q',
	    'PoSH_H R[QaQ RZWaW Rc[f[ Ri[l[ RZCZN R[SZW R^N^[ RbEfEf[ RhElEl[ RUDWG RTUWVR[ RfPbT RlPhT R^D\\G RcIdN RiIjM RYI_L RUOSU RURYQUY RZHVLRM',
	    'PoTE^E RRJ_J RTP^P RTS^S RTW^W Ra[e[ Rh[l[ RTMTW RVGVK RYCYG R\\H\\K RTM^M^W R`EeEe[ RgElEl[ R[Y_Z ReP`U RlOgT RaIbN RhIjN RWXRZ',
	    'PoTF\\F RRI^I RRMWM RYM^M RYP^P RRS^S RgWjW R[[k[ RXCXI R[S[Z R[K[P RhGgK RbM`R RfEjEkWk[ RcH`K RiMgR RVSTZ R_EdEdW`W RUKVPRQ',
	    'PoTI]I RTL]L RTO]O RSQVQ RZQ^Q RSTVT RZT^T RRW^W R`[d[ Rh[l[ RTETO RXPX[ RXCXF RTE]E]O R_EdEd[ RgElEl[ RkHgL RbQ_T RjOhS RcH_L',
	    'PoRJ_J RTU^U RTY^Y Rb[e[ Ri[l[ RTQT[ RSEYEYP RYQYY RTQ^Q^[ RaEeEe[ RgElEl[ ReP`U RlPgT RYJSP RUFUI RbIcN RhIjM RYK]M R]F[J R]DUE',
	    'PoVOiO RVRiR RSTkT RRWlW RVLVQ RZRZW R_L_Q RdRdW RVLiLiQ RUFXG RbFdG R[HRI RSD]D]JZJ RhH`I R`DkDkJgJ R[YR[ RcYl[',
	    'PoZF`F RRJaJ RTMaM RUP[P RVWXW RVEVJ RZCZJ RaXa[ RXPYVVZ RjIgL R^WZ[ R]K_L RkNhT RlMjR RVRUU R[SYX RdGbK R`Q\\Y RdObU RfMcS RaDfDf[c[ RgDlDl[i[ R]J]O^Z RTMTUR[',
	    'PoSI_I RRK`K RTS^S Rb[e[ Rh[l[ RSDSH RWDWH RYSY[ R[D[H RSD_D_H RaEfEf[ RgElEl[ R_U\\V RjIgL RVWRX RYSTX RXU^W RkNhT RlMjR RdGbL RdObU ReMcS RTOTS RTN^O^S',
	    'PoRE_E RSH^H RRK`K RTM]M RSP_P RRR`R Rb[e[ Rh[l[ R[[][ RSTSZ RXMXO RYCYH RYU^T][ RaEfEf[ RgElEl[ RYV[X RjIgL RkNhT RlMjQ RdGbL RdObU ReMcS RSTWTWYRY',
	    'Po^HcH RfHkH R]KcK ReKkK RRM\\M R_PkP R_SkS R_VkV R_ZlZ RWDWM RYMYX R`N`[ R]DcDcK ReNeZ ReDkDkK R_O\\R RSFTK R[FZK R]VXX RUMUSTZ',
	    'PoUGfG RRMlM R[[j[ R[Q[[ R]C]M RjDbL RkWj[ RbMRT RhR[V',
	    'PoUJfJ RRQlQ R]F]Q RiI^T R\\VR[',
	    'PoUGfG RSLlL Ra[g[ R^C^L RYTiTg[ RZPYU RhOZQ RkEeJWRRR',
	    'PoUFfF RRIlI R[M^NkN RbTiT R^UfU RZUaU RWU]U ReWlW R^XfX RWX^X RTYZY R[K[N R^C^I R_R_[kZk[ RjE`IRM RiK_L R]RWR RfPZR RhWbW\\X',
	    'PoUGfG RRMlM RYUhU RYZhZ RZQZ[ R]C]M RjDdKaLRS RZQhQh[',
	    'PoUFfF RRIlI R[PiP RVViV RVZiZ RVSV[ RZLZP R^C^I RVSiSi[ RjE_JRN R`MZN RhL]M',
	    'PoRKlK RYWcW Rb[h[ RYRYY RYRcRcW RiDfH R]C^KWSST RUFeGZNRP R[PjPh[',
	    'PoUFgF RRIlI R[LaL R[OkO RXXcX Rc[h[ RXTXY R[K[O R]C]I RXTcTcX RZOSV RjDaIRM RXRjRi[ RhK_L',
	    'PoUFeF RRJlJ RWWaW R_[e[ RWSWX R]C]J RWSaSaW RiDeH\\NUN R^LRP RZLYPhPf[ RgL[M',
	    'PoUFeF RRKlK RUOkO RUShS RVWiW RR[l[ R^C^J R_S_[ R]NYS RcPhS RiEfG[MRO',
	    'PoUFfF RSIlI R[MaM R[OkO RSRlR RUXjX RS[l[ R[K[O R^C^I R_U_[ R\\RYU RTUgTjV RjEaIRN RiK^L',
	    'PoRElE Re[j[ RTKT[ R[K[Z RcKcZ RTKjKj[ R^E]K',
	    'PoSDkD RfOiO RRRlR RUHUO R[H[N RbHbN RUHiHiO R[RVW R]PYS R_E]H RWVk[ RfRbW^YZZR[',
	    'PoSEkE RfPiP RRTlT RUIUP R\\I\\P RbIbP RUIiIiP R_E]I R`UdYk[ R_R]V[YR[',
	    'PoREaE R][`[ RSJS[ RXJX[ R[J[[ RSJ`J`[ RiDfGbI RjKfObQ RlSgYb[ RXEWM',
	    'PoRE`E RaJlJ R\\[_[ Re[i[ RSKS[ RXKXZ R[K[Z RSK_K_[ RiCi[ RcNeS RXEWK',
	    'PoTIjI RRMlM Rf[j[ RTDTI RTQT[ R[Q[[ R_C_I RcQc[ RTQjQj[ RjDjI R_M]Q',
	    'PoYG`G RTH\\H RULjL RRQlQ R_C_[ R_PeWlZ R_PYWRZ RiE_G',
	    'Po`FlF RSK^K RRO_O Rc[g[ RYCY[ RgFg[ RXP^V RVTRX RYNUU R^ERG',
	    'PoSK]K R_OlO RRO]O Ra[e[ RXCX[ ReKe[ RXQ]V R`FkEeK RUURX RXNTW R]ERF',
	    'PoSF\\F R_IlI RSK[K RRO\\O R^QlQ RWCW[ RhCh[ RVO[U RaW][ RWNSW RbCbQaW',
	    'PoSJ]J RRO]O RaSeS RXCX[ ReCeR RXQ]V RUURX RiHlO RXNTW RjQhUeX^[ RaH_O R\\ERF',
	    'PoSF\\F RSK\\K RRP]P Rd[k[ RXCX[ RdFdZ RlWl[ RWP\\U RWPSW RiD^G RkK_M RkQ^S',
	    'Po`EjE RSJ]J R_NkN RRO^O RcZkZ RaZiZ R^ZeZ RXCX[ RXQ\\U RUURX RhTl[ RXNTV RdN`Z R]ESF',
	    'PoRK]K R_OjO RRP]P R`[k[ RXCX[ R`D`Z ReDeO R_DjDjP RXQ\\U RlWk[ RXNTW R\\DRF',
	    'PoSF\\F RSK[K RRP\\P RXCX[ RdCdN RWP\\U R`NbTgYl[ R^HkHjL RXNTW R^NjNgTcY^[ R^H^R\\Z',
	    'PoSF\\F RSK\\K R^MjM RRO\\O R`ZjZ RXCX[ R`Q`[ R`QjQj[ RWO\\U RiHkN RWNSW RdC`K',
	    'PoSK]K R`LkL RRP^P R`ZlZ RXCX[ R`D`[ R`DkDkL R`QlQl[ RXQ\\U RXNTV R]ERF',
	    'PoSK]K R_LiL RRP]P R_SiS R\\ZlZ RXCX[ R_E_Z R_EiEiZ RWP\\U RXNTW R\\ERF',
	    'PoRO[O RfYlY R_[b[ RWCW[ RfGf[ RfGlGlZ RWQ[U RWNSX R[ERG R_C_O]Y RSK\\JcIcVb[',
	    'Po^ElE RRK]K RRP]P R`SjS R]ZlZ RXCX[ R`E`Z R`KjKjS RXQ\\U RXNTW R\\DRF',
	    'PoSJ\\J R`MiM RRO\\O R_ZjZ RXCX[ R_R_[ R_RjRj[ RWP\\U RfGlM RbH]M ReCgH RXNTW RcCaI R\\ERF',
	    'PoSK[K R]KbK RRO[O R]QbQ Rg[k[ RWCW[ R]E]X RWQZT RgCgRc[ RWNRX R]EbEcWZX R[ERG RdJlJlUk[ RgLfW',
	    'PoRFZF R\\FlF RSJZJ RRO[O Rd[h[ RWCW[ R]L]O RaCaI RhChI R]LlLlO RWNYRZU RcW^[ RVNRW RdObX R_RjRh[',
	    'PoSF\\F R`KiK RSK[K RRP\\P R^TjT RWCW[ R^O^[ RcOc[ RfOf[ RVP[U RdDjK RcE]K RdCcF RWOSX R^OjOj[h[',
	    'Po^GkG RSK]K R]MlM RRO]O R_UiU R_ZiZ RXCX[ R_P_[ RaCaM RhChM R_PjPj[ RXQ\\U RXNTW R\\ERF',
	    'PoSF]F RSK\\K RRO\\O RbVhV Rh[k[ RXCX[ R^L^[ RbObW ReCeK RbOhOhV R^LkLk[ RWO\\T RkEjI RUURX R_EaI RXNTW',
	    'Po_IkI RSJ\\J R_MkM RSO]O R`VjV Rh[l[ RXCX[ R^P^[ R_E_M ReEeV R_EkEkM R^PlPl[ RXQ\\U RhSjX RXNTW R\\ERF',
	    'PoRF[F R]IlI RSK[K RRP[P R\\SlS RWCW[ RdCdO RYQ[U R_D`H RiDhH RWNYS RdHhLlN RWNSX RbP`U RdH`M]N R_WkZ RhUdY][',
	    'Po^DlD RaGjG R^JlJ RRK\\K RRP\\P R_TlT Re[h[ RWCW[ RbJbP RhQh[ RaVcY RWQ[U RjLgM ReKhOlP RWNRX ReP`P R[ERF R^D^Q]X',
	    'Po]FkF RRF[F R^IkI RSK[K R\\LlL RRP[P R^SjS R\\WlW Rf[j[ RWCW[ R^O^[ RaCaL RdLdV RgCgL R^OjOj[ RWNYRZU RWORX',
	    'Po^FkF RSF[F RSK[K RRP\\P R^PkP Re[i[ RWCW[ R^K^O RdMeP ReCeF R^KlKlO RWQ[U RaW][ RbGbJ RiGgK RWOSX RcQaY RbUjUi[',
	    'Po]EkE RRF[F R^HjH RSJ[J R]KlK RRO[O R_QjQ R_TjT R_WjW RWCW[ R_M_W RdCdK R_MjMjW RYQZU RWNYS RgYl[ RWNRX RaX\\[',
	    'Po\\HlH RSK\\K R^KjK RRO\\O R^QkQ R]TlT RXCX[ R^N^Q R^E^K R^EjEjK R^NkNjR RWQ[U RWNSW RdCcQaU_WkZ RiTeY\\[ R\\ERF',
	    'PoSJ[J RRO\\PlQ RWCW[ RlXl[ RfXi[ R_TbW RWQ[U R]H_J RfGhJ RhOjP RiGgN RWORX R_D^H RaG^N RhDgH RjTeX`[ R^Q\\Z R[ESF RaLbN\\N RjKkNeM RdCdLfX',
	    'PoSK[K RRP[P Re[i[ RWCW[ R]N]Q R]NkNkQ RWQ[U R`HcK RhHlL RkEjH RWORX R]E]H RcEbH ReEeH R^K\\L RfKdL RcW`Z\\[ R`C_J RdPbX RhCgJ R[ERF R]SjSi[',
	    'Po]ElE RRF[F R^KjK RSJ[J R_MjM R_OjO RRO[O R_RkR RWCW[ R_I_S RaCaG ReIeR RgCgG R_J\\M RWNYS RWORX RaWfYl[ R]UjUgWcY\\[',
	    'Po\\DkD R_IiI RSJ[J R_LiL RRO[O RaSgS R_ViV RWCW[ R\\N\\Q RcDcG R_GiGiN R_G^NkNkQ RWQ[U RbOeP RiQkS R_Q\\S RWORX RaQbSaV\\W RbXl[ ReY[[ R[ERF',
	    'Po]FlF RRFZF RfIlI RSJZJ RRO[O R_QkQ RaZjZ RWCW[ RbSb[ RbGbO RhGhO R`UjUj[ RVOZT R_IbIdL RhIlN RdCeF RbS^W RWORX RbH_N RhHfN R]F]R[Z',
	    'Po[ElE RRFZF RSJYJ RRPYP RVCV[ R_V_[ RdNdQ R\\HkHkL RcCdE RVNXT RdThYlZ RVORW RaR\\R R\\H\\LkMfO RdS[W RlTfW RaHaM RfHfM RfQlR RdY^Z R\\NaO',
	    'PoRElE RWKgK RWQgQ RSXiW RWEWX RgEg[',
	    'PoUJ]J RUP]P Rc[k[ RUEUV RRE^E][ RcDcZ RlUkZ R]URW',
	    'PoRE`ElF RUJ]J RUP]P Rb[f[ RUEUV R]E][ RfFf[ R\\URW',
	    'PoRE`E RUJ]J RUP]P RfWkW RUEUV R]E][ RbDb[ RgMlRlV RiHgM RbElEiJ R]URW',
	    'PoSFkF RYPeP RYTeT R^XjX RZXgX RTXbX RYLYX RVLgLf[ RbFfJlM R^DXJRM',
	    'PoRMlM RWQgQ RWTgT RWXfX RRXbX RWMWX RgMg[ RXFSK RdF_K RWH\\J RdFgJkK RYCWI RdCbI RkWgX',
	    'PoTJ[J R`LhL RTP[P RTDTV RRD\\D[[ ReElL RcCbG]L R^QjQe[ R[URV',
	    'Po`FkF RUK\\K R^NlN RUQ\\Q RcZkZ R`ZiZ R^ZfZ RUEUW RRE]E\\[ RiTl[ RcNaZ R\\VRW',
	    'PoTJ[J RfMlM RTP[P R^ZlZ RTETV RRE]E\\[ R`J`Z RfCfZ R[URW',
	    'PoUK\\K R^LlL RUQ\\Q R^SlS Rf[j[ RUEUW RRE]E\\[ RaEa[ RaEjEj[ R[VRW',
	    'PoTJ[J RTP[P Rf[l[ RTETV RRE]E\\[ R_H_L ReCeNfZ RbV^[ RlWl[ RdQ`Y R_HlHlL ReHcV R[URW',
	    'Po^IlI RUJ[J RUP[P RcYiY R`ZfZ RUEUV RRE\\E[[ RhSl[ RbCaK^Y ReMcY RbG_X R[URW',
	    'PoTJ[J RTP[P RTETV RRE]E\\[ RgVl[ RbV][ ReCeQiY RdQaY RkIhP ReNgW R`I^P ReNbW ReKfU ReKcU R[URW',
	    'PoTJ[J RTP[P RTETV RRE]E\\[ RfDjG RgS_[ RlWk[ ReWh[k[ RiMfT RdK^L RcCcKeW RkJgKeK`K R[URW',
	    'PoTJ[J RTP[P Rc[h[ RTETV RRE\\E[[ RfFjN RbF^M RbO`X R]NiNiTh[ R]URW',
	    'PoSEkE RWHgH RWKgK RRNlN RSR]R R_RjR RWEWN RgEgP RbRcUfYl[ RTT[X R\\SXXR[ RiSeX^[',
	    'PoTJZJ R]NlN RTPZP R\\SlS RTDTV RRD[DZ[ R^H^[ RdCdS R^HjHj[f[ RZVRW',
	    'PoTJ[J RTP[P RTETV RRE\\E[[ ReElL ReK`O RcF]L RcCbH RgNcS_T RjReX^[ R[URW',
	    'Po_HkH RTJ[J R]OlO RTP[P RTDTV RRD\\D[[ RgUlZ RcU]Z ReHePhW RaD^K RdOaX R\\TRW RdKcT',
	    'PoTJ[J R`LhL RTP[P RfXjX RTETV RRE]E[[ RcPc[ R^PjPjX ReDlK RcE]K R[URW',
	    'Po^ElE RTJ[J ReOkO RTP[P R\\ZlZ RTETV RRE\\E[[ R`K`Z ReEeZ R[URW',
	    'PoTJZJ RTPZP RTETV RRE[EZ[ RaD]N RbK^U Rj[g[ RbM\\N R[URW RaRcV\\V RcIlIlTj[ RgCgMeX',
	    'PoTJ[J R_MkM RTP[P R_YlY RTETV RRE\\E[[ R_Q_Y R_F_M ReCeY RkFkM RlQl[ R\\URW',
	    'PoTJZJ RTPZP RiUlU RTETV RRE[EZ[ R^F^T RgEg[ RgElElU RbW][ RdD^F RcHcT\\T RYURV RcObW',
	    'PoSGkG RaMlM RRPlP RWSgS RWVgV RTZgZ RWPWY RaGaM RgPg[ RkKlM RcDgE R]C[IXKRN RhI[M RjYfY',
	    'PoTJ[J R_PkP RTP[P RTETV RRE\\E[[ R_E_P R_EkEkP RhTk[ RaT^[ R]URW',
	    'PoTJZJ R]NlN RTPZP Ra[d[ RTDTV RRD[DZ[ R]H]K ReNe[ R]HlHlK RdCdF R\\URW',
	    'PoTJZJ RTPZP Rg[l[ RTETV RRE[EZ[ RgDg[ RlXl[ RfNkS RbO^T RaV][ R^H`L RkGhL RcDbRaW R\\URW',
	    'Po]ElE RTJZJ RTPZP RTETV RRE[EZ[ R^K^[ RbKbZ RgKgZ RdEbM R^KkKk[i[ RZURW',
	    'PoRD^D R]HlH RTJ[J R^NkN RTP[P R_ZjZ RTDTV R[D[[ R_R_[ RdCdN R_RjRj[ R[URW',
	    'PoTJ[J RaKjK RTP[P RaQjQ RTETV RRE\\E[[ RaEaW R_EkEj[ R^URW RlU^W',
	    'PoTJ[J R^KlK RTP[P R`ZjZ RTDTV RRD]D\\[ R`R`[ RfEfR R`RjRj[ RjD_F R[URW',
	    'PoSE\\E RhMlM RROlO RWRfR RWVfV RUYfY RRYeY RWOWY RXEXL RfOf[ R`DhDhM RlJlM RbHeK R_J\\N RjXfX R\\KRM R`E_L',
	    'Po^IkI RTJ[J RTP[P R]PlP RTDTV RRD[D[[ RfUk[ R_DbG RbU][ RiEdIdQgX RdP`X RdMbV R]URW',
	    'Po^HkH RTJ[J R\\NlN RTP[P R_ZkZ RTETV RRE\\E[[ R_R_[ RdCdN R_RkRk[ R`D]L R[URW',
	    'PoUH]H RUK]K RbOkO RSO]O RUSjS RVWhW RR[l[ RUDUO R_S_[ RbEbO RbEkEkO RRE]E]P',
	    'Po]GlG RTJZJ RTPZP RTDTV RRD[DZ[ ReVk[ RhMkP RcU][ R_LaQ RdCdRgX RiJgR R`J]R RdMcV R\\URW',
	    'Po^JkJ RTJ[J R^NkN RTP[P R\\QlQ Rc[h[ RTETV RRE\\E[[ R^F^M RdCdM R^FkFkM RaQ_UjUi[ R[URW',
	    'Po]EkE R^IkI RTJZJ R\\LlL RTPZP R_RjR R_VjV Rf[j[ RTETV RRE[EZ[ R_O_[ RdCdL R_OjOj[ R[URW',
	    'PoSE`FkF RUH^H RUK^K RXN^N RSOZO RUEUN R^E^O R`R`[ RbIkN RaQbTfXl[ RYQST RjGfL`O R[UXWRZ RiSdU RePZS',
	    'PoTJZJ R]KjK R]PjP RTPZP R]UlU RTDTV RZDZ[ R]F]P RbCbF RfQf[ RRD]EjFkP RcP_T RdKaR RdHcP R[URW',
	    'PoTJZJ RTPZP R_QkQ R]ZlZ RTETV RRE[EZ[ R^G^J ReQeZ RdCfG R^GlGlJ RfJkO RcJ^O R[URW',
	    'Po]HlH RUJ[J RUP[P R^RcR RUDUV RRD\\D[[ R^L^R R^LcLcR RiDkF RgTa[ RjMfV RdU]V RlWk[i[ RgCfPhZ R[URV',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWOgO RZRdR RZUdU RVYdY Rg[l[ RRER[ RZOZX RRE\\E\\L RaEaL RdOd[ RaElEl[',
	    'PoTF^F RRI_I RaLkL RROlO RWRgR RWUgU R\\XkX RXYgY RRYbY RWOWY RaEaL RgOg[ RaEkEkL RVDSG RYJ_M RXKRM RYEXK',
	    'Po^IkI RUK[K RUQ[Q R]QlQ RUEUW RRE\\E[[ RhIh[ R`DaH RiCgH RaIaR`X R[VRW',
	    'PoTJ[J R^OjO RTP[P Rb[h[ RTDTV RRD\\D[[ RbTb[ RhXh[ RfKjO RgElI RiTlY RbQfS RaC^I RcH`O R^T]Y RZURW',
	    'PoTJ[J R^LjL RTP[P RdRjR Rd[l[ RTDTV RRD\\D[[ R^G^J ReLeZ R^GkGkJ R`VdZ R_TaY RdCdG R_P^Z RZURW',
	    'PoTJZJ R^KbK RfKjK RTPZP R^PjP Rf[l[ RTDTV RRD[DZ[ R_E_P RfPfZ ReEjEjP RlXl[ R_Z\\[ RbD^E RaQ`Y R\\URW',
	    'Po]FlF RTKZK R_MjM RTPZP R_SjS Ra[d[ RTETW RRE[EZ[ R]P]S R_I_M ReCeG ReSe[ R_IjIjM R]PlPlS RZVRW',
	    'PoTJZJ R_MlM RTPZP RbWjW R`WgW Ri[l[ RTETV RRE[EZ[ R^Q^[ R_H_M R^EeEeV R_HlHlM R^QlQl[ RhSjX RkDbE R\\URW',
	    'PoTJZJ RTPZP RfQiQ Ra[h[ RTETV RRE[EZ[ RbTb[ RgXh[ RcRfU ReLaP R`F\\J RbJ]O RiTkY R`JgM RaC_I RcGaL R_U\\Z RgGdN R`GkGjP RZURW',
	    'PoTJYJ R_NiN RTPYP R\\SkS RTETV RREZEY[ RcSk[ RgJlN RiEfG RcU^Z R]H`J\\N ReEiL R]EcE`K RlHhJ R[URV RdNcU',
	    'PoTJYJ ReOlO R\\OcO RTPYP R\\TlT RTDTV RRDZDY[ R`P`W RhPh[ RaLcO R\\G_J RjLlO ReGhJ RjGgM R_C^H RaG^O RhCfH R_Z\\[ RYURV',
	    'Po^IjI RTJZJ R]LlL RTPZP RTDTV RRD[D[[ R^E^I R^O^W RdCdL R^OjOjW ReXkZ RdVaY\\[ R^EjEjI RdRcX R\\URV',
	    'PoTJZJ R_OjO RTPZP Ra[h[ RTDTV RRD[DZ[ R_I_O RbSb[ RhXh[ R_IjIjO RcRfU R`DbG RiTlY R^T]Y RhCfI R\\URV',
	    'PoTJYJ R\\NbN ReNkN RTPYP R\\SkS RTDTV RRDZDY[ RbKbO RdReUjZ RiIdM RaH\\N RjKkN R_D]I RgCeI RcU`Y\\Z RcPcV RaH\\I R[URW RiHdI',
	    'PoTJYJ R`LiL R^LgL RTPYP R_[b[ Rh[k[ RTDTV RRDZDY[ R\\PbPb[ RdPkPk[ R]R`T RfRhT RfIjM RaD\\I RcG]M RfDiGlH RbU\\X RkUdX R[URW',
	    'PoTJZJ R^KkK RTPZP R^PkP RfWlW RTETV RRE[EZ[ R^F^P R^FkFkP RcCbF RkTlW R^Z\\[ RhQgW RaP_Y ReFeZlZl[ RZURW',
	    'PoTJYJ RTPYP RTDTV RRDZDY[ RcVc[ RjVkZ RjGeR R`D\\L RbH]R R^V\\[ RhDdK RiKdK RaK\\L RbNbR\\R RjNjRdR R[URW RfVfZ',
	    'PoTJYJ ReNlN R\\NcN RTPYP R]VaV RfVkV RTETV RREZEY[ R]Q]W RbPbW RfPf[ RkQkW RaLcO R\\G`J RjKlO ReGhJ RhKeN RjFgL R`C^H RaF^M RhCfH R`Z][ RYURV',
	    'PoTJ[J RTP[P R^PjP Rb[h[ RTETV RRE\\E[[ R^F^P RbTb[ RhXg[ R^FjFjP RcIhIcN RcCbF RiUlZ RdG`KhM RbRfS R_U]Z RZURW',
	    'PoTE^E RRG_G RRJ`J RRPkP RWSgS RWVgV RRYkY RWPWY RYCYJ RgPg[ RVL]L\\O RcHfLlO RcC`I RWJUMRN RbGjFhJeL`N',
	    'PoRE^E RSG^G RhHlH RSM]M RRPlP RWSgS RWVgV RWYlY RTYjY RRYhY RWPWY RXCXG RXIXL RTI]I]L RgPg[ RbDhDhG RaIeMlN RbDaH RTIRO R`IjIgLdM_N',
	    'PocJjJ RaNlN RRPlP RWSgS RWVgV RUYkY RRYhY RWHWO RWPWY RdHdN RgPg[ R^EaF R_DZH R]KZN RgCbH RfEkG RWDRF RXGUIRK R_MgN',
	    'PoRE]FkF RTJ[J RTP[P R^QkQ Rb[h[ RTETW R[E[[ R^J^Q RbUb[ RbJbQ RdCdJ RfJfQ RhXh[ R^JkJkQ RdSfU RiUlZ R_U\\Z RZVRW',
	    'Po^IjI RTK[K R\\KlK RTP[P R^QjQ R^TjT R^WjW RTETW RRE\\E[[ R^E^I R^N^W RdCdK R^EjEjI R^NjNjW RgYl[ RaY\\[ R[VRW',
	    'PoTEkE RYJfJ RTMhM RUR]R RbRjR RUU]U RbUjU RcXlX R_XjX RRY]Y RUOUX RYEYM RRO^O][ RbObX RYGfFfM R`OkOj[',
	    'Po\\FeF RTJYJ R[LlL RTPYP R]SdS R]WdW RTETV RREZEY[ R]O]Y R]H^K R`C`F RbGbK R]OdOdW RiDkG RhUb[ RjOfX RiZk[ RlWk[ RgCgMgPiZ RYURV',
	    'Po]IlI RTJYJ R`OlO RTPYP RaQjQ RaTjT RaZkZ RTDTV RRDZDY[ RaVa[ RaVkVk[ ReLfN RgJkK R`EfEeH RcJaL RaD]H^S\\[ R[URV',
	    'PoTJYJ RaLgL RTPYP R\\RlR R\\UlU R`[d[ RTETV RREZEY[ R^N^R RaIaL RbNbR RdCdF RdUd[ RfNfR R^NjNjR R]F]GkFkH RiIlL R_J\\L RbHeH RYURV',
	    'PoTJYJ RTPYP RaPgP R]TkT Ra[d[ RTDTV RRDZDY[ RdTd[ R\\F\\GlFlH R]N`O RhVkY RfMlR R_LbM\\R R_V\\Z R_J[M ReJgO RcCdF ReJjJhN R_JcJ`O R[URV',
	    'PoYEbE RdHlH RYIaI RYLaL RYNbN RRQlQ RWVgV RRYlY RWQWY RYGYL R]C]P RYGbGbL RWTgRg[ RiHiP RRGVH RTDWE RVJUN RkDdEdKcO',
	    'Po]FlF RTNZN R^NkN R]QlQ RTSZS Ra[h[ RTITX RSEYEXI RRI[IZ[ R^I^N RbTbZ RbIbN RdCdI RfIfN R^IkIkN RiUkY RcSfT R^U]Z RgXh[ R[WRX',
	    'PoRE]EkF RTH[H RTL[L R^OjO RSP[P R]RlR RSRYR RSVYV Rb[h[ RTETP RVRVY R[E[[ R^I^O RbUb[ RbIbO RdCdI RfIfO RhXh[ R^IjIkO RjVkZ RbSfU R_V]Z RYYRY',
	    'PoaEkE RSE^E RRH_H RaIjI RaOjO RTN]N RRSlS RWWgW RTZkZ RRZiZ RTJTQ RWSWZ RTJ]J]Q RWUgTg[ RaGjGjI RbIaQkPlQ RVEUH R[E[H RaCaG RTLWLTL RZL]LZL RaKdKaLdMaM RgKjK RfMiM RdMgM RhKeK RhMeM RdKgK',
	    'PoRJlJ RUNiN RTRjR RRWlW R_C_[ RUFiFiN',
	    'PoRJlJ RUNhN R^C^[ RUFhFhN',
	    'PoUF]F RUKjK RWPlP RXSiS RXVjV RVXlX R`L`[ RXNiNiS RUHjHjK RUEUQTY RhC^E',
	    'PoRE\\E R]JlJ R^OjO R]SkS R\\WlW RdCd[ R^FjFjO RWVR[ RUPYR RZOVY RVESQ RUJ[JXV',
	    'PoRJlJ RVNiN R_C_[ RVEiEiN RiPi[ RcReW R[RYX RUQTY',
	    'Po^JlJ R_NjN RTO]O R_RkR RRS]S R^VlV RTCTK\\J[K ReCe[ R_FjFjN RYW\\Z RWU[Y RWWS[ RUMRR R[ESG RXPWW',
	    'PoRHlH RULiL RUQ[Q RdQiQ RUTiT RdWiW R[N[Q RUNUW[W[[ R_C_[ RdNdQ RdWd[ RUEiEiL RiNi[ RUQTZ',
	    'PoTE\\E RTI\\I R]JlJ RTM\\M R_NjN RRQ]Q R_RkR R]VlV RdCd[ R_FjFjN RTEUPTZ RZT\\YRZ',
	    'PoSD^D RaFkF RTK]K RRQkQ RVShS RUViV RSYkY RTG]G]K R_M_[ RVNhNhS RcD`GdJkM RiFfJ`L RTGSN',
	    'PocFkF RaGhG RTK]K RRQlQ RVShS RUVjV RRYlY RTG]G]J R_M_[ RVNhNhS RbDdIgKjM RfDiD RlKlM RiHbL RTETISM R\\DTE',
	    'Poe[l[ RRHR[ RRHlHl[ R^JhN R^RhV R^I[MVO R_N]R[UUW R_D_I',
	    'Po_ZkZ RX[[[ RTD[D[[ RVIZL RlVlZ RYPVS R[OXR RbP^Y RcNaS ReLcP RgIeM R_FjFgJ RTDTSS[',
	    'PoTK\\K RTR\\R RX[[[ Re[j[ RTD\\D\\[ RcR_Z][ R^JlJlTk[ RTDTSS[ RdCdMbV',
	    'PoTK[K RTR[R Rh[l[ RW[[[ RTD[D[[ R`DhDiZ RlVl[ RTDTSS[ R`D`S_Y',
	    'PoTK[K RTR[R Rh[l[ RW[[[ RTD[D[[ R]JhJi[ RlVl[ RaR^Z RbCbO`W RTDTSS[',
	    'PoWNhN RWThT Rb[h[ RRDRH RWIhIh[ RRDlDlH RTZR[ RWIWRVX',
	    'Pod[i[ RTMT[ RTMiMi[ R\\MeQ R^SdV RZD_DbHjK R_J]N[QWR R^S[VWW R_D\\GRK',
	    'Po`HlH R_[k[ RW[[[ RTD[D[[ RVJYL RkWk[ R`MjM_X RbC^M R[PUR RTDTSS[',
	    'PoTK\\K RTR\\R RX[\\[ Rd[j[ RTD\\D\\[ RaNgS RcC^M RaIlIlSj[ RTDTSS[',
	    'PoTKZK RTRZR RV[Z[ RTDZDZ[ R]OeS RlYl[ RaR^Z R]IhIhTiZ RTDTSS[ RbCbM`V',
	    'PoRFlF RSKjK RTRhR RTVhV Rc[h[ RRFRK RTOT[ R^C^F RTOhOh[',
	    'PoTKZK RTRZR R_[k[ RV[Z[ RTDZDZ[ R_F_Z ReCeU RlWkZ RkIdKaL\\N RkHkTgT RTDTSS[',
	    'Poe[j[ RW[[[ RTD[D[[ RWJYL R`McQ R_LbO RcR_[ R[PUR R_ElElNlTj[ RTDTRS[ RdEdLbW',
	    'PoTPjP RTUjU Rd[j[ RTKT[ R_C_K RTKjKj[ RRDWI RkDfJ',
	    'Po]JlJ RTK[K RTR[R RW[[[ RTD[D[[ RgWk[ R^ShW RhNeUbY\\[ RiKfU RbD_S RTDTSS[',
	    'Po]JlJ RTK[K RTR[R RW[[[ Rc[h[ RTD[D[[ RhCh[ R`NcT RTDTSS[',
	    'PoSIkI RRQlQ RRUlU Rd[l[ RRMR[ RRMlMl[ RSEkEkJ RSETJ',
	    'PoTK[K R^LlL RTR[R R]ZlZ RW[[[ RTD[D[[ ReCeZ RTDTSS[',
	    'Po_FkF RTK[K RTR[R R^ZlZ RW[[[ RTD[D[[ ReFeZ RTDTSS[',
	    'PoTK\\K RTR\\R RX[\\[ RTD\\D\\[ RiDfG_J RjKgO`R RlRgW^[ RTDTSS[',
	    'Po^EkE RTK[K R]NlN RTR[R RW[[[ RTD[D[[ ReEe[ RTDTSS[',
	    'PoTK[K RTR[R RW[[[ RTE[E[[ RbGfK RfS^Z R_KcThYl[ RiJdU R^EbR RkEgR RTETSS[',
	    'Po_EjE RTK[K R]KlK RTR[R RW[[[ Rb[i[ RTD[D[[ R`K_RkRj[ RTDTSS[',
	    'PoTKZK RTRZR Rf[j[ RV[Z[ RTD[D[[ R]EiEeI RbP]U ReT^[ RhLdV RcL`S RdI^LlLlRj[ RTDTSS[',
	    'PoTK[K RhLlL RTR[R RW[[[ RTD[D[[ R`DhDhL R`PaTeXl[ R^M\\M RlIlL R^PjPhTdX\\[ RTDTSS[ R`D_L',
	    'Po]HlH RTK[K RTR[R RW[[[ RTD[D[[ RdCdN R_OaTfXl[ R]NjNhSbX\\[ RTDTSS[',
	    'PoTK[K RTR[R RW[[[ RTD[D[[ RcKeM RdDlL RcF]L R^QjQe[ RdCaH RTDTSS[',
	    'Po^IkI RTK[K R]PlP RTR[R RX[[[ RTD[D[[ RfVk[ RbV][ RdCdQhX RdP`X RdMeT RTDTSS[ RdLcU',
	    'PoTK[K R_OkO RTR[R R_[k[ RW[[[ RTD[D[[ R_E_Z ReEeO R_EkEkP RlVk[ RTDTSR[',
	    'PoTKZK RTRZR Rd[h[ RW[Z[ RTEZEZ[ R`W\\[ RaEgEkM R`G]M RbO`X R^OjOiTh[ RTETSS[',
	    'Po\\FlF RTKZK RTRZR RV[Z[ RTDZDZ[ RdId[ RgLlQ RcJ\\Q ReFbL RTDTSS[',
	    'Po^HkH RTK[K R^NkN RTR[R R]TlT RW[[[ RTD[D[[ ReCe[ RTDTSS[',
	    'PoSElE RVLjL RYSjS RYWjW Re[j[ RYOY[ RYOjOj[ RVHjHjL RVHVQTX',
	    'Po]HlH RTK[K RTR[R Rd[i[ RW[[[ RTD[D[[ RbNkNjUi[ RTDTSS[ RdCbO`Y^[',
	    'PoTKZK RTRZR R^SjS Rd[k[ RW[Z[ RTEZE[[ R^K^V RdCdZ RjKjU RlWl[ RTETSS[ RjF]H',
	    'Po]JlJ RTK[K RTR[R Rd[k[ RW[[[ RTD[D[[ RdCdZ RgDjG R`W\\[ RbQ_Z RlVk[ RTDTSS[ RcIaV',
	    'PoTK[K RTR[R Rg[l[ RW[[[ RTD[D[[ R^I^[ R^IlIl[ RfPjT RdP`T R_DeDeMbS ReHfP RTDTSS[',
	    'Po]HlH RTK[K RTR[R Rg[l[ RW[[[ RTD[D[[ R_MgMhZ RbCcG RlVl[ RTDTSR[ R_M_S^X',
	    'Po_GiG RRKlK RVRhR RVVhV Rc[h[ RVNV[ RWEWK R_C_K RVNhNh[',
	    'PoTKZK R\\KlK RTRZR RW[Z[ RTDZDZ[ RgVl[ RgDjH RaV\\[ RePiY RbQ_Y RcCcMgW RcN`X RcHeR RTDTSR[ RcJaV',
	    'Po\\IlI RTKZK RTRZR RbYjY R`ZhZ RV[Z[ RTDZDZ[ RhSl[ R`O\\[ ReLbY RTDTSS[ RaG^W',
	    'PoRFlF RSLjL RWShS RWVhV Rc[g[ RWOW[ R_C_F RWOhOh[ R\\FWL RdHiK',
	    'PoTKZK RTSZS Re[i[ R`Q\\U RcV^[ RgLbX RaC\\M RdH_S RhIeT RTEZEZ[W[ R_IlIlPj[ RTETSS[',
	    'PoSLlL RYShS RYWhW Rc[h[ RYOY[ RXPhPh[ R]KWQRS RXDiI RgD]GUI',
	    'PoTKZK R_MlM RTRZR RW[Z[ RTEZEZ[ RgMg[ RTETSR[ RiD_E_R]Y',
	    'PoTKZK R]LbL RTRZR ReZlZ RV[Z[ RTDZDZ[ R]C][ ReCeZ RlWlZ RkKeM RcY]Z RTDTSS[',
	    'PoTK[K RTR[R RW[[[ RTD[D[[ R`HlHjM RgVl[ RbV][ ReQhY RdQaX RdMgW RaC^L RdMcV RdKeT RTDTSS[',
	    'PoTK[K RTR[R R\\RlR RW[[[ RTD[D[[ RdMd[ RaEgEjL R`G^M RTDTSS[',
	    'PoTKZK R^LiL RTRZR RW[Z[ Rb[g[ RTEZEZ[ R`EgDjK R_F\\L RaL_SiRg[ RTETSS[',
	    'Po\\GlG RTKZK RTRZR RgWkW RV[Z[ RTDZDZ[ R^L^W RdDdG RdId[ R^LkLkW RTDTSS[',
	    'Po^FjF RWRhR RWVhV Rb[h[ RWNW[ R^C^J RWNhNh[ RRJRN RRJlJlN',
	    'Po]JkJ RTKZK RTRZR R]RlR RV[Z[ RTDZDZ[ RhCh[ R^Z\\[ RaCaS`X RTDTSR[',
	    'PoTKZK R\\KlK RTRZR RW[Z[ RTDZDZ[ RhWl[ RbWeZ RaV\\[ ReOiY RcP_Y RdCdLgV RdKaW RdIeR RTDTSS[ RdHbT',
	    'PoTSgS RTWgW Rb[g[ RRERM RTPT[ RXDXN RTPgPg[ R]EbJjN R\\EiEeJ\\N',
	    'PoTK[K RTR[R R^RlR RW[[[ RTD[D[[ R^I^T ReCe[ R^IlIlT RTDTSS[',
	    'PoTKZK R\\NlN RTRZR RV[Z[ RTDZDZ[ R`D`[ RgUlZ RjEcK RdNhW ReY^Z RTDTSS[',
	    'PoTKZK RTQZQ Rb[f[ RTEZEZ[ R_M\\T RbQ^Z RjMkT RcDcN`X RW[Z[W[ RTETSS[ RcIaU R^HhHhTg[',
	    'PoTK[K R^MlM RTR[R R^SlS RW[[[ RTD[D[[ R^G^U ReCe[ R^GlGlU RTDTSS[',
	    'PoRIlI RRMlM RUSiS RUWiW Rd[i[ RRERL RUPU[ R_E_L RUPiPi[ RRElElL',
	    'PoRIlI RRMlM RTSjS RTWjW Rd[j[ RRERM RTPT[ R_C_M RTPjPj[ RRElElM',
	    'Po_IjI RTKZK R\\OlO RTRZR RW[Z[ RTEZE[[ RfUk[ RbU\\[ RdCdPhX RdO`X R_E]M RTETSS[ RdLcU',
	    'PoTK[K R`MjM RTR[R R`TjT R]ZlZ RW[[[ RTD[D[[ R`E`T R`EjEjT RTDTSS[',
	    'PoTKZK R]MkM RTRZR RV[Z[ RTDZDZ[ RfCf[ R]GkGkM R`W\\[ RkXiX R\\M\\RlRkX RTDTSS[ RaCaR`W',
	    'Po\\IlI RTKZK RTRZR ReUjZ ReDhE RfU`Z RaCaNfW RiNeW RaIdT RTEZEZ[W[ RTETSS[ RaJ_W',
	    'Po_QlQ R_ZlZ RW[[[ RTD\\D\\[ R_H_[ R_HlHl[ RWJYL RfCdH R\\PVR RTDTSS[',
	    'PoTKZK RTRZR R_TfT RV[Z[ Re[j[ RTEZE[[ R_L_V R_LfLfT RaC]K R_HlHlSj[ RTETSS[',
	    'PodIlI RTK[K RTR[R R^ZjZ R^P^[ RdCdP R^PjPk[ RTE[E[[X[ RTETSS[',
	    'PoSF[F RWRhR RWVhV Rc[h[ RWNW[ RWNhNh[ RbCbLlLlJ RjEbG R[C[KRK',
	    'PoTKZK RTRZR RV[Z[ RTDZDZ[ RiDiLiOkZ RcGdY^Y RTDTSR[ RjD^F^R\\[ RfThY`Y',
	    'PoTK[K R_MkM RTR[R R_ZjZ RW[[[ RTD[D[[ R_Q_[ R_QjQj[ RgHjKkO RcC_M RTDTSS[',
	    'PoTK[K RTR[R RhXkX RW[Z[ RTD[D[[ ReCe[ RdR]X ReOcT R^PlPkX RTDTSS[ R^I]Q RkG^I',
	    'Po^MlM R^YlY RV[Z[ RTE[E[Z R^Q^Y R^F^M ReCeY RlFlM RlQl[ RVIYL RYPVS RTETSS[',
	    'Po_QkQ RW[[[ RTD[D[[ R_E_P R_EkEkP RVJYL RiTl[ RaT^Z R[PVR RTDTSS[',
	    'Po^JkJ RTKZK RTRZR Rb[k[ RW[Z[ RTDZDZ[ RcLcZ R^EkEkJ RlWk[ RkObS RTDTSS[ R^E^R\\Z',
	    'Po]EkE RTKZK R\\QlQ RTRZR RV[Z[ RTDZDZ[ RdEd[ R_H`N RjHhN RTDTSS[',
	    'PoYG^G Re[j[ RTFTL RUPU[ RUPjPj[ R]PgT R_UfX R_M^Q\\RXT RbCaLlKlL R_T\\WXY RjFaG RYCZLSM R^KWL',
	    'PoaJlJ RTKZK RTRZR Re[i[ R_G_[ RiCi[ R_IZN RbNeT R`C\\L RTDZDZ[W[ RTDTSS[',
	    'PoTK[K R^MjM RTR[R R]SlS RW[[[ RTD[D[[ RdCd[ RjGkK R`E^K RTDTSS[',
	    'PoTKZK RTRZR RW[Z[ RTEZEZ[ RdCkK RdD\\K RdL^O RgOdR^T RjSfW][ RTETSS[',
	    'Po\\HlH RTK[K RTR[R RW[[[ RTE[E[[ R\\ZjYk[ RgUkX RePdS RbU^Z R]N`OcSbV RhLfQ RdCcH_O RdGaM RTETSS[',
	    'Po_HlH RTK[K RcMkM RTR[R RcSlS RW[[[ RTD[D[[ RcHc[ RaC]L RTDTSS[',
	    'Po]FlF RTKZK RTRZR R\\ZlZ RV[Z[ RTDZDZ[ RdIdX RfLkR RdK\\R ReFbM RTDTSS[',
	    'PoTK[K R^LlL RTR[R R^SlS RV[[[ RTD[D[[ R^E^T ReEe[ R^ElElT RTDTSS[',
	    'Po_JkJ RTK[K R^RkR RTR[R R]ZlZ RW[Z[ RTD[D[[ RdCdZ R`E]N RTDTSS[',
	    'PoTKZK R^MlM RTRZR R]ZfZ RV[Z[ RTDZDZ[ R^F^V RlUkZiY RjD^F RfEfNgX RTDTSS[ ReU\\V',
	    'PoTKZK R^QfQ RTRZR R_[k[ RV[Z[ RTDZDZ[ R_Q_[ R^KfKfQ RlWk[ R`C\\L RTDTSS[ R_GkGkPjUgU',
	    'PoTKZK R\\NlN RTRZR R]WlW RV[Z[ Rd[h[ RTDZDZ[ RbPfT RcGfK R_EjEjOjUi[ RTDTSS[ R_E_I_L^P]W',
	    'Po^HkH RTK[K R\\OlO RTR[R RdYkY R`ZgZ R]ZdZ RW[[[ RTD[D[[ RiTl[ RdCcO`Z RTDTSS[',
	    'PoRI_I RcKlK RcRlR RTX^X Rg[l[ RTPTY RYCYO RTP^P^W RcElEl[ RcEcQaX',
	    'Po^ElE RTKZK RTRZR R^TeT RV[Z[ Rc[i[ RTDZDZ[ R^J^U R^JeJeS RiEi[ RTDTSS[',
	    'Po_HlH R`[k[ RV[Z[ RTDZDZ[ R`K`[ ReIeW RjLjU RVJYL RkXk[ RaC^J RZPUR RjM\\O RTDTSS[',
	    'PoWMeM RZScS RZVcV Rh[l[ R`[c[ RZPZ[ RZPdPd[ RhDh[ RYF]J RbJfM R^DZG RaF[M RlWl[ RUDURTY',
	    'Po_HhH R]MlM RWShS RWWhW Rc[h[ RWPW[ R_E_L RWPhPh[ RSEjEjH RVJRN RWFUL RZKcM',
	    'PoTKZK R\\QlQ RTRZR RV[Z[ RTEZEZ[ R^H^P R^HiHiP ReUjZ RbU][ RcCcRgX RTETSS[ RcMbV',
	    'Po\\IlI RTKZK RTQZQ Rf[l[ RfIf[ RlWl[ RjNiRaY RdEiG R`S\\[ RTEZEZ[W[ RTETSS[ RaCaM_W',
	    'Po[GlG RTKYK R]PkP RTRYR RU[Y[ R`[d[ RTDYDY[ RdKd[ RiTkY RaF]P R_T\\Y RbC_N RTDTTR[',
	    'PocGjG RTK[K R]NlN RTR[R RW[Z[ RTD[D[[ RcCc[ RdSjU RTDTSS[',
	    'PoeFlF RTKZK R_QkQ RTRZR RW[Z[ RTDZDZ[ ReCeJ R_KkKkS RTDTSR[ R_K_S^Z',
	    'PoTKZK R]QkQ RTRZR R\\ZlZ RV[Z[ RTDZDZ[ RdQdZ R]EiEcKlN ReJ[N RTDTSR[',
	    'Po`EkE RRG^G Rf[j[ RUPU[ RUPjPj[ RbFfKlN R^VfX RjFfKaM R_N^RXU R^T^VXY RXCWFTJ^M R^RgT R^HZLRN',
	    'PoTK[K R`LjL RTR[R R^ZlZ RW[[[ RTE[E[[ R^E^[ R^ElEl[ ReGePiV RdOaW ReIfS RTETSS[ ReJcS',
	    'PoTKZK R^QjQ RTRZR R\\WlW R^Q^W ReNe[ RbD\\J R`GeLlO R`FiGdL\\O RTEZEZ[W[ RTETSS[',
	    'Po\\GlG RTKZK R`MhM R]PkP RTRZR RV[Z[ Rc[g[ RTDZDZ[ RgIlN RaI\\N RcC_K RaP`UiTg[ RTDTSS[',
	    'Po\\FlF RTKZK R^OjO RTRZR RW[Z[ RTDZDZ[ R]JjJkN ReUjZ RcU^[ RdCdSfX RdRaX RkWiV R^N]SkRkW RTDTSS[ RdNcV',
	    'PoTKZK R\\NlN RTRZR Rf[l[ RV[Y[ RTDZDZ[ RdCdN RgNgZ RlWl[ R_W\\[ R^E`K RjEgK RaN_X RTDTTR[',
	    'Po]GlG RTK[K RTR[R RfUl[ R_McQ RbS]T RhK`T RcI_M RdFbK RjQcX][ RTE[E[[W[ RdDdI RTETSS[',
	    'PoTKZK RTRZR R_ZiZ RW[Z[ RTEZEZ[ R_R_[ R_RiRi[ RaF\\K RaGbJfOlQ RbC_I R`GiGgJbO\\Q RTETSS[',
	    'PoaIiI RTK[K RTR[R RbThT Rh[l[ RW[[[ RTD[D[[ R^E^[ RbMbV RbMhMhT R^ElEl[ RTDTSS[',
	    'Po]ElE RTK[K R_MkM R\\MfM RTR[R R]SkS R\\ZlZ RW[[[ RTD[D[[ RdOdZ RgIkM RbE_M RTDTSS[',
	    'Po\\GlG RTKZK RTRZR RW[Z[ RTEZEZ[ RdCdG RgJlN R`J\\O R`ObTgXl[ RgNeT`Y\\[ RTETSS[',
	    'PoVMfNkN RWTiT RWWiW Rd[i[ RVIVN RWPW[ RfIfM RWPiPi[ RYCSJ R[HcK RcG[K RXFlFkN',
	    'PoTKZK RTRZR R^VhV RV[Z[ RTDZDZ[ R^L^X RhLhX R`MfS R`C\\K ReJaS RTDTSS[ R_GlGlTk[e[',
	    'Po\\ElE RTKZK RTRZR RV[Z[ RTDZDZ[ R]K][ RbJb[ RfKf[ RcEbN R]KkKk[h[ RTDTSS[',
	    'PoTKZK R\\NlN RTRZR R]G]K RdCdG R]GkGkK R_ThXjZ RhOfUcX\\[ RbI_U RTEZEZ[W[ RTETSS[',
	    'PocEkE RTKZK RbLlL RTRZR R_L_[ R`D[I R_L[P R`I^N RTDZDY[V[ RiLh[d[ RTDTSS[',
	    'Po]IkI RTKZK R\\QlQ RTRZR RV[Z[ RTDZDZ[ RhIh[ R_DaH RiCgH RTDTSS[ RaIaR_X',
	    'PoaNkN RTQ]Q RTU]U Ra[k[ RY[][ RTMT[ RTM]M][ RbCbN RbPb[ RRJ]H^J R[F^H RkKkN RlWk[ RWCTI RjFaH RjRaU',
	    'PoSF`F RRKlK R\\Y`Y RSNSZ RZCZK RSN`N`Y RgDjG RYTVW RgT`[ RZNVS RYP^R RgZj[ RYT]V RjMeW RlWk[ RcCcKeW',
	    'Po^HkH RTK[K R]MlM RTR[R R^SkS R\\ZlZ RW[Z[ RTE[E[[ RdCdM RdOdZ RTETSS[',
	    'PoWNfN RVThT RVWhW Rc[g[ RVQV[ RVQhQh[ RbGfKlN RSH[HXKRN RjGdJ RVEgE_G_M[M',
	    'PoTKZK RTRZR Rf[l[ RV[Z[ RTEZEZ[ RfCfZ RlWl[ RgPkT RkHgL R]H_L RaQ\\S R]Z\\[ RTETSS[ RaCaR`X',
	    'PoTK[K R_MkM RTR[R R_UjU R_ZjZ RW[Z[ RTD[D[[ R_C_L R_Q_[ R_QjQk[ RlIlL RjF_H RTDTSS[',
	    'PoeJiJ RTKZK RTRZR R^RkR R_[k[ R_L_Z RdLdR R^LkLkT RlWl[ RbG]L RbE`J RTDZDZ[W[ R]EjEiJ RTDTSS[',
	    'Po]GlG RTKZK RTRZR RV[Z[ RTDZDZ[ RgWl[ RdV_[ RfIfRiY RfQbY RkMiR R_D_L\\U RbM`S RTDTSR[ R_G^S ReNeV',
	    'PodHhH RYN]N RhNkN RWThT RWWhW Rc[h[ RWQW[ RWQhQh[ RVEiEhH R`KlKkN RXKTO RRK]K]N RfKcN`O RaD\\GVI',
	    'Po]JlJ RTKZK RTRZR RfUjU RbZkZ RV[Z[ RTDZDZ[ RcNcZ RbNjNjT RlWkZ R`FhFeJ RTDTSS[ RaC^J^S]Z',
	    'PoTKYK ReMjM RTRYR Rh[k[ RV[Y[ RTDYDZ[ R\\QcQcZ ReQlQlZ Rb[_[ RcC`L R]FkFjM RTDTSR[ R_O^Y RhOfY',
	    'PoTKZK RTRZR RV[Z[ RTDZDZ[ RcKc[ RkNhQ RkI`K RTDTSS[ RjD^F^S\\Z RfJhTk[',
	    'PoTKZK R\\NaN RTRZR RV[Z[ R`[d[ RTDZDZ[ R^IdId[ RhTlX R_T[X RkKfP RdKiV R`CfF RaO^V RdIgS RTDTSR[',
	    'PoTFZF RcFkF RSJZJ RdJkJ RXRfR RXVfV Rb[f[ RXMX[ RVNgNg[ R_CbIeMlP R_C\\IXMRP',
	    'PodGhG RZM]M RhMkM RXShS RWWhW Rd[h[ RRI]I\\M RXPhPh[ RVDiDhG R`IlIkM RWKTN ReL`N RUZT[ RXQWY R^GUH',
	    'Po`HlH RTKZK R`MkM RTRZR R\\UlU RV[Z[ RTDZDZ[ R`M`U RfGf[ RaC]L RTDTSS[',
	    'PoTKZK R_MiM R]QkQ RTRZR RaZkZ R\\ZgZ RW[Z[ RTEZEZ[ RgUjX RdClK RcD\\K RbQ_Z RTETSS[',
	    'PoTKZK R]OlO RTRZR RW[Z[ Ra[d[ RTDZDZ[ RdLd[ RhSjY R^EkK R`S]X RiDfG]K RTDTTR[',
	    'Po^GlG RTKZK R`PkP RTQZQ R_ZlZ RfIfZ RcCeE RTDZDZ[W[ RTDTTR[ R^G^S\\[',
	    'Po\\GlG RTKZK RTRZR RV[Z[ RTDZDZ[ RcCeF RhQh[ R`IdMjP RiGgJdN]P RTDTSS[ RaQ`X',
	    'Po\\HlH RTKZK RTQZQ R]YkY RX[Z[ RTDZDZ[ R]K]Y RkKk[ RdR`V R`KbMePgV RcCdF RgJbU RTDTTR[',
	    'PoTKZK R[NlN RTRZR RV[Y[ RTDZDZ[ RcCc[ RgSlX R`T[X R^E_K RcLePhU RcL`T RjEgK RTDTTR[',
	    'PoTKYK RTRYR RV[Y[ RTDYDZ[ R\\H\\K R`O`Z R\\HlHlK RgVlZ RaN[S RiQfR RkNhQ RdNiX RdG_P RcIgU RdDaN ReY_Z RTDTSR[',
	    'PoRFlF Rg[j[ RTOT[ R[F[M RbFbM RTOjOj[ R^CaF RgHkL RWHSL R_TfX R_M^QXT R_S\\WXX R^QgS',
	    'Po^RkR Rd[l[ RV[Z[ RTDZDZ[ R^I^Q ReJeZ R]JkJkQ RlWl[ RVJYL RcU^Z R`G\\J RaC_H RZPUR RTDTSS[ RaFhFeJcV',
	    'Po]FlF RTKZK RTRZR R\\TlT RW[Z[ R`[d[ RTEZE[[ R]K]N RdCdK RdRd[ R]KlKlN R_OiOfR RTETSS[',
	    'PoTK[K RgOjO RTR[R R_ZkZ RW[Z[ RTD[D[[ R_R_[ R_RkRk[ ReMbP RbJ^N R`C^I RcF`M RhFdO R_GlGkO RTDTSS[',
	    'PoTKZK R_KiK R\\PlP RTRZR Rf[l[ RW[Z[ RTEZE[[ R]G]J RgPgZ R]GkGkJ RlWl[ R`W\\[ RdCdG RaP`X RTETSS[',
	    'Po\\HlH RV[Z[ R_[d[ RTDZDZ[ RdCd[ RVJYL R]L`O RgTlY RgDjF RaS\\W RkLgP ReNiW RZPUR RdJgT RTDTSS[',
	    'Po[HcH RTKYK R[NdN RTRYR R\\XdX RV[Y[ RTDYDY[ RfDf[ RfDlDlV RaSdY RiVlViV R_C_M]X RTDTTR[',
	    'Po]ElE RTKZK RTRZR R^SkS R\\[l[ RV[Z[ RTDZDZ[ RdSd[ R]K_P RcMeQ RgLjO RjGhL R_G]L RdGbL RTDTSS[',
	    'Po\\ElE RTKZK R^OjO RTRZR R^TjT R^ZjZ RW[Z[ RTDZDZ[ R^J^[ R^JjJj[ RdEbJ RTDTSS[',
	    'Po\\FlF RTKZK R^NjN RTRZR RV[Z[ R_[d[ RTDZDZ[ R^I^N RdTd[ R^IjIjN RcCdF R]QjQdT RTDTSS[',
	    'PoTKZK RTRZR R]TkT R[ZlZ RW[Z[ RTDZDZ[ RdCdZ RjMkP RhKkN R_JbP R_F]P RiFfO RTDTSR[',
	    'PoTKZK R\\QlQ RTRZR RV[Z[ Rc[h[ RTDZDZ[ RhNh[ R^SbW R^H_L RcGeK RjFiL RTDTSS[ RjD^E',
	    'PoTKYK RgMkM RTRYR RbVlV Rb[l[ RU[Y[ RTDYDY[ RcJcV RgEgV R]VaZ R\\R_X R^S[[ R[F^F[N R_M[M R_M]X RkDaF RTDTTR[',
	    'PoTKXK RbMkM RTRXR RbUlU Rc[l[ RU[X[ RTDXDY[ RgEgU R^WZ[ R[E`E[M`M^W RkDbF R\\Q^WaYiZ RTDTTR[',
	    'Po_GkG RTKZK R\\QlQ RTRZR R^XlX RV[Z[ Rd[h[ RTDZDZ[ RbSeV RcLeO R`C]K R`JjJjUi[ RTDTSS[ R_L^W',
	    'PoUEkE RYGiG RUJlJ RWObO RWThT RWWhW Rc[h[ RWQW[ RZJZO RWQhQh[ RUJRP R`JbLfNlP RiKcM RUFTN',
	    'Po^EkE R`IjI RTKZK R^NkN RTRZR RTEZEZ[ RaNaZ RhWl[ RfRiY RkRfS ReNhX RW[Z[W[ RTETSS[ R^E^R\\[ RgY^Z',
	    'Po]GlG RTKZK RTRZR RV[Z[ RTDZDZ[ ReVk[ RjNkR RiLjP RcU][ R_LbQ RdCdRgX RiJgR R`J]R RTDTSS[ RdMcV',
	    'Po[IlI RTKYK RTRYR R_YcY RU[Y[ RTDYDY[ R`P`Y RcVcY R_LbN RhDkF RdQfV R\\Q[W RlVl[ RgCgMiZ RTDTSS[',
	    'Po\\IjI RTKZK RTRZR R`RiR RgEkJ RaQ\\V R_SdXk[ RaC_I RaI_M\\N RfHeNkMkN RhSdX][ RTDZDZ[W[ RTDTSS[',
	    'Po]GlG RTKZK R^QjQ RTRZR RV[Z[ RTDZDZ[ R^K^Q RdCd[ R^KjKkQ ReRlY RdQgT RcS\\Y RTDTSS[',
	    'Po_GkG R_SiS R_WiW Re[i[ RWHW[ RZFZW R_P_[ R_PiPi[ R`F\\J R_GcLkO RXDSN RaC_H RiGdL\\O',
	    'Po\\LlL RV[Z[ RTDZDZ[ R_P_Y RdNd[ RVJYL R`N\\R RbI^Q RiD]J RZPUR R]DkI R]RkQjXgX RTDTSS[',
	    'PoaEgE RTKZK R^QjQ RTRZR Rf[l[ RW[Z[ RTEZE[[ R^K^Q RfQfZ R^KjKjQ RlWk[ RhFlJ R`W\\[ R`G\\K RaQ_Y RTETSS[',
	    'PoTKZK RTRZR R\\TlT RW[Z[ R`[d[ RTDZDZ[ RdQd[ RcGdK RiFlL R_F\\L R^MjMgPdQ RjD^E RTDTSS[',
	    'PoTK[K R^KjK R]OkO RTR[R R^TjT R\\ZlZ RW[Z[ RTD[D[[ R^D^K RdOdZ R^DjDjK RTDTSR[',
	    'Po]FcF RTK[K R^PlP RTR[R RW[Z[ RTD[D[[ R_H`L RdGeK R`UkZ RjGhL RdM`U RiPgVdX^[ RTDTSS[ RkD`E',
	    'Po]GlG RTKZK R^PkP RTRZR R^TkT RV[Z[ Rh[k[ RTDZD[[ R^K^[ ReCe[ R^KkKk[ RgDkE RTDTSS[',
	    'Po]EkE RTKZK R_QjQ RTRZR R\\ZlZ RV[Z[ RTDZDZ[ R_J_Q R_JjJjQ R`SaX RhSgY RTDTSS[',
	    'PoTK[K R^PjP RTR[R Rf[l[ RW[Z[ RTD[D[[ R^I^P RgPg[ R^IjIjP RlXl[ R`DbG RhCfI RTDTSS[ RbP`W][',
	    'Po]JkJ RTKZK R_PcP RTRZR RW[Z[ Rb[e[ RTDZDZ[ ReLe[ R]DkDkJ RhUlY RkOgR ReNiW RcQ`Y RTDTTR[ R]D]S\\[',
	    'PoTKZK RTRZR R^YkY RV[Z[ RTDZD[[ R^M^[ RlMl[ RaOcPiU ReR`W R]E^J RkDiJ RgLdS RcDdI RTDTSS[',
	    'Po]ElE R_YjY RW[Z[ RTEZEZ[ R_R_[ ReGeP R_RjRj[ RVJYL RaK\\M RfE_L RfJkK RZPUR RTETTS[',
	    'Po^KjK RTK[K R]NkN RTR[R R\\SlS RW[Z[ RTE[E[[ R^E^K R^EjEjK RdRfWhYl[ RcU`Y\\[ RdOeV RTETSS[',
	    'Po_JkJ RTKZK RTRZR RW[Z[ Rh[l[ RTDZDZ[ R]O][ R_D_J R_DkDkJ R]OlOl[ RdQjV RdR`W ReObU RTDTSS[ ReKdS',
	    'PoTKYK R\\LeL RTRYR RU[Y[ RTDYDY[ RaEa[ RgFgU R`MeR R`K\\U RlDl[h[ RdD\\E RTDTSR[',
	    'PoTKZK R_LhL RTRZR R[ZlZ RcDlM RcF[M RjOeZ RcCaH R^P_V RTEZEZ[W[ RcOdU RTETSR[',
	    'Po_DjD R_HiH RTKZK R_LiL R\\PlP RTRZR RV[Z[ RTDZDZ[ R_D_Z ReUj[ RjRgU RcPfX ReY\\Z RTDTSS[',
	    'Po\\HlH RTKZK RTRZR R\\TlT RV[Z[ RTEZEZ[ RdCdG RdQd[ R`LcO RhMkQ RhJeO R`J]P RTETSS[',
	    'Po\\FlF RTKZK R\\MlM R]QjQ RTRZR RdUkU Rb[l[ RW[[[ RTEZEZ[ RdCdZ R]JjJjQ R^VbZ R^T[[ RTETSR[',
	    'Po\\ElE RTKYK RTRYR RV[Y[ Rh[k[ RTDYDY[ R]J][ RdEd[ R]JkJk[ R`O_V RhOeV RaNbV RhNiV R`N^M RgNeM RTDTSR[',
	    'Po^IkI RTKZK R_NkN RTRZR R_TkT R_ZlZ RW[Z[ RTEZE[[ R_H_[ RfGfY RfDeK RTETSS[ RaC^I_M',
	    'Po^KjK RTKZK R^OjO RTRZR R\\UlU RV[Z[ RTDZDZ[ R^F^O RfQf[ R^FjFjO RdFdO`S RcCbF RTDTSS[',
	    'Po]GjG R]JiJ RTKYK R\\OkO RTRYR RU[Y[ RTDYDY[ RaRaZ ReSjX RdCdO\\U RjQgU RcNfU RdY_[ RcKdR RTDTSR[',
	    'PoTKYK RTRYR R\\TkT RV[Y[ RTDYDY[ R\\O\\[ RaOaZ RfOfZ RcDjK R^KhJlK RcD[L R\\OkOk[h[ RTDTTR[',
	    'Po\\IcI ReIlI RTK[K RdQlQ RTR[R RW[Z[ RTE[E[[ RhHh[ RiDhJ RcP\\Q RaC`I`S_X RTETSS[',
	    'Po^IjI R^NjN R\\RlR RV[Z[ RTDZDZ[ R^D^N RdDd[ R^DjDjN RVJYL RdRlY RcS\\Z RZPUR RTDTSS[',
	    'Po]IjI RTKYK R\\MlM RTRYR RdWgW RV[Y[ R`[j[ RTDYDY[ R`R`[ R_RgRgV RgNlS R]E`G RiDgG R`N[S RjXj[ RcH_Q RdCaO RTDTSS[',
	    'Po\\JkJ R\\ObO RfOlO R]UjU R\\ZlZ RV[Y[ RTDZDZ[ R_L_R R^EdEeZ RiLiR RVJXL RZPUR RTDTSS[ RiDaE',
	    'PoTKZK RTRZR RW[Z[ RTDZDZ[ RiEhI RjQhU RcJjM RdNdUgYl[ R_F^I R_Q]U RcJ`M\\N RdRcW`Y\\[ RcDcK RTDTSS[',
	    'PoTKZK R]MjM RTRZR R[TlT RV[Z[ RTDZDZ[ R]G]T RbCbT RfCfT R]GjGjT RgVk[ RaV][ RTDTTR[',
	    'Po\\IlI RTKZK RTRZR R[RlR RW[Z[ RTEZEZ[ RdEdO RdHgMlN RcH`M[N RaP^VkZ RhRgVdY\\[ RTETSS[ RiD^F',
	    'Po^FkF R^IkI RTKZK R]LlL RTRZR R_SjS R_VjV RV[Z[ Rf[j[ RTDZDZ[ R_O_[ RdCdL R_OjOj[ RTDTSS[',
	    'Po]KbK ReKjK RTKYK R]QjQ RTRYR RV[Y[ Re[l[ RTDYDZ[ R^F^P RfQf[ ReEjEjP RlWl[ R_W\\[ RcD]E R`Q_X RTDTTR[',
	    'Po]GkG RTKZK R\\MlM RTRZR R_UiU R_ZiZ RV[Z[ RTDZDZ[ R_P_[ RaCaM RgCgM R_PiPi[ RTDTSS[',
	    'Po[GkG RTKYK RcLkL RTRYR RV[Y[ RTDYDY[ R^K^[ RcCcG ReUk[ ReNgQ RhTaZ RcNfW RjMfW R_H[P RdI`S RTDTSR[',
	    'Po\\GkG RTKZK R^QiQ RTRZR R^ViV RV[Y[ Rd[k[ RTDZDZ[ R_L_W RdJd[ R]MjMjV RlXk[ RfHlN RbH\\N RdCaJ RTDTTR[',
	    'PoTKZK RTRZR R`[h[ RV[Z[ RTDZDZ[ RaTa[ RgXh[ RcSeW RbJeL RcEjL RbG\\M RiUkZ R^NiNfT RcCaI R]U\\Z RTDTTR[',
	    'PoRD]D R`EkE RRK]K RRN]N RVSgS RVWgW Rb[g[ RRDRN RVPV[ RWDXG RWKXN RRG]G]K RVPgPg[ RaGfLlN RjFfK_N',
	    'PoTKZK RTRZR RV[Z[ RTDZDZ[ RgVl[ R`UcX RgHkM RjHdN RaU[[ RjUdZ RaJ]N ReQiY R^HcJ R]TaV ReEkEhK RdQkPhX R]EcE_L R]QcQ_X RTDTSS[',
	    'PoTFlF R^IlI RdOhO Rf[j[ RXRX[ R[I[O R`C`F RhGhO RXRjRj[ R`KbM RZJVL R\\GXK RaSgU RaWgY RaP`S[U RaU_X[Z RTFTSS[',
	    'Po]FlF RTKYK RcMlM RTRYR RV[Y[ Rf[i[ RTDYDY[ RbKb[ RdCdG RjHj[ RdPfU RbH^Q RTDTSR[ R]F]T[[',
	    'Po^JkJ RTKZK R^NlN RTSZS R`ZkZ RW[Z[ RTDZDZ[ R`S`[ ReKeS R`SkSk[ R^DkDkJ RTDTSS[ R^D^R\\Z',
	    'Po]HbH RfHlH RTKZK R]NbN RfNkN RTRZR RfTlT RV[Z[ RTDZDZ[ RfCf[ RaW][ RbCbT\\T RTDTSS[ RbPaX',
	    'PoTKZK RTRZR R^RkR R\\ZlZ RV[Z[ RTDZDZ[ R]F]J RdCdG RdRdZ R]FlFlJ RgFgNlMkN R_N\\O RbF`M RTDTSS[',
	    'PoTKZK R^LcL RTRZR RgTkT Rf[l[ RV[Z[ RTDZDZ[ R]F\\J RdDdF RfKf[ ReKkKkT R\\FlFlJ RlXl[ R]PbS^Z R_I\\R RcM`X RTDTSS[',
	    'Po\\FlF R]NkN R]SkS RW[Z[ RTEZEZ[ R]J]S RdCd[ R]JkJkS RVJYL RdSkZ RdR\\Z RZPUR RTETSS[',
	    'Po^JjJ RTKZK R^OjO RTRZR R\\RlR RW[Z[ RTEZEZ[ R^F^O RdCdO RhRh[ R^FjFkO R_Z][ RaR`Y RTETSS[',
	    'Po_IiI RTKZK R`NiN RTRZR R_UiU R]YlY RV[Z[ RTDZDZ[ R]D][ RdIdU R]DlDl[ RgQhS RTDTTR[',
	    'PoTK[K R`KiK R]QkQ RTR[R RW[Z[ Ra[d[ RTD[D[[ R]G]J RdQd[ R]GkGkJ RhTkW R_U[Y RcCeG RTDTSS[',
	    'PoTKZK R^LkL RTRZR RdRkR Rd[l[ RV[Z[ RTDZDZ[ R]G]J ReLeZ R]GlGlJ R_TbY RcCdF R_P^Y RTDTSS[',
	    'Po^IkI RTKZK R^LlL R`RkR RTRZR R_VkV R][l[ RV[Z[ RTDZDZ[ ReReZ R^DkDkI RgNkR ReL_Q RTDTSS[ R^D^S\\Z',
	    'Po\\FlF R_KiK R_OiO R]SlS RV[Z[ RTDZDZ[ R_C_O RdOd[ RiCiO RVJYL RgUlZ R`W\\[ RZPUR RTDTSR[',
	    'Po\\ElE RTKZK RiQlQ RTSZS R\\TlT RW[Z[ RTEZEZ[ R]I]Q RaIaP RdEbI RfIfP R]IlIlQ RdSeWgYl[ RcV`Y[[ RdQbX RTETSS[',
	    'Po_JhJ RTKZK R\\LlL RTRZR RV[Z[ RTDZDZ[ RhRkY RcN]Q RdR]U ReTbW\\Z R`C_I R`EiEgL RkOdQfVe[a[ RTDTSS[',
	    'Po]KkK RTKZK R]PkP RTRZR R^UkU R\\[l[ RV[Z[ RTDZDZ[ R]F]I RdCdF RdQdZ R]FkFkI RgMkQ RbK_P RTDTSS[',
	    'Po\\FkF R]JjJ RTKZK R[MlM RTRZR R\\UjU RW[Z[ RTEZEZ[ RfMkR R`M[R ReJgO RcTdWfYk[ RbG^P RbW_Z\\[ RcC`N RcQaX RgP^Q RTETSS[',
	    'PoeHjH RTKZK RTRZR RbWhW Ri[l[ RV[Z[ RTDZDZ[ R^N^[ RbRbX ReHeN RbRhRhW R`DjDjN R`D_NlNl[ RTDTSS[',
	    'Po^IlI R^MlM RW[[[ Ri[l[ RTD[D[[ R^O^[ R^D^M R^DlDlM R]QlQl[ RWJYL R[PUR ReDeV`W RTDTSS[ RhSiWbW',
	    'Po]FlF RTKZK RTRZR RV[Z[ RTDZDZ[ R\\L\\O R^Q^X RdNd[ R\\LlLlO R`GaJ RdCcF RhGgL R^QjQjXgX RTDTSS[',
	    'Po]GlG RTKZK R\\MlM RTRZR R_UjU R_ZjZ RV[Z[ RTDZDZ[ R_P_[ R`H`K RdCdG R_PjPj[ RiHgK RTDTSR[',
	    'Po^IjI RTKZK R^MjM R^RkR RTRZR R^UkU R\\ZlZ RV[Z[ RTDZDZ[ R^D^M RdNdZ R^DjDjM R_N\\T RTDTSS[',
	    'PoTKZK RTRZR R^ZkZ RV[Z[ RTDZDZ[ R^P^[ R^PkPk[ RdThW RaSeT R]H_K RbHfL RhHlL RkDhG RcMcP RdCbH R_C]H RhQcV`W RTDTSS[',
	    'PoTKYK RTRYR RV[Y[ R`[b[ RTDYDY[ R\\O\\[ R\\ObOb[ R^QaR R^U`W R^EcDjK RcD\\K RdTg[ RhTk[ R_KgJlK RfNdT RjNhT RTDTSR[',
	    'Po]JkJ RTKYK R[MlM RTRYR Ri[k[ R\\R\\[ R]D]J RaRa[ RdCdJ RfRf[ RkDkJ R\\RkRk[ RdMbR RTDYDY[V[ RTDTSR[',
	    'Po\\GlG RTKZK R`QiQ RTRZR R_UiU Rh[k[ RW[Z[ RTDZDZ[ R]K][ RdCdK RdQdZ R]KkKk[ RaMaO RgMfO RTDTSS[',
	    'Po\\IkI RTKZK R]PjP RTRZR R]TjT R\\WlW RV[Z[ RTDZDZ[ R\\D\\G R]L]S RdGd[ R]LjLjS R\\DkDlG RTDTSR[',
	    'Po`EgE R\\HlH RTKZK R^OjO RTRZR R^SjS R]WkW R\\[l[ RV[Z[ RTDZDZ[ R^K^S R]EeEd[ R^KjKjS RTDTSS[ RjDbE',
	    'Po^EjE RbOeO R\\SlS RV[Z[ RTDZDZ[ RdQd[ RfHfN RdSkY RVJYL RdS]Y R]IkIjM RaGhI RiFdH RdIaL\\N RZPUR RTDTSR[',
	    'Po^JcJ RfJkJ RTKZK R^MjM R\\QlQ RTRZR RW[Z[ Rc[h[ RTEZEZ[ R^E^J R^EcEcJ RfEfJ RfEkEkJ R`Q_ViUh[ RTETSS[',
	    'Po^JjJ RTKZK R^OjO RTRZR Ra[h[ RV[Z[ RTDZDZ[ R^E^O RbSb[ RdEdO RhXh[ R^EjEjO RiTlY RbPfR R^T\\Z RTDTSS[',
	    'Po^IlI RTKZK R`RkR RTRZR R`VkV R`ZkZ RW[Z[ RTEZEZ[ R`N`[ RfDfN R`NkNk[ RTETSS[ R^E^R\\Z RcE^E RjD`E',
	    'Po\\ElE RTKZK R]NkN RTRZR R\\RlR RV[Z[ RTDZDZ[ R]H]N RbEbN RfEfN R]HkHkN RbO_WkZ RhRgVdY\\[ RTDTSS[',
	    'Po`JlJ RTKYK RbMjM RaQkQ RTRYR R`UlU Rb[l[ RU[Y[ RTDYDY[ RfCfX RbFjFjM R^S[[ R[E^E\\N_M]X R\\R^WaZh[ RTDTTR[',
	    'Po^IjI RTKZK R^MjM R\\QlQ RTRZR RV[Z[ RTDZDZ[ R^D^M R_Q_[ RdDdM R^DjDjM ReUk[ RjUfV RdQgX ReY^Z RTDTSS[',
	    'PoTKYK RTRYR RiVlV R]YcY RV[Y[ RTDYDY[ R]Q][ R]QcQcZ RfDf[ RfDlDlU R`JdN RbDdI R^D[I R_J\\P RTDTTR[',
	    'PoTKZK R]KbK RfKkK R]QbQ RfQkQ RTRZR RV[Z[ RTDZDZ[ R]F]R RfFkFkR ReUk[ RcU][ ReCdRgX RdQaX RaE]F RdNeU RTDTSS[',
	    'PoTKYK R\\KbK RdKkK R\\PbP RTRYR R\\UbU RU[Y[ RTDYDY[ R\\E\\[ R\\EbEbK RdEkEkK RgVl[ RdPhY RcPkPiVeYa[ RTDTTR[',
	    'Po\\JaJ RTKYK RiMlM R\\PaP RTRYR RV[Y[ RTDYDY[ R\\E\\[ RdDiDjM RlJlM RfVk[ RdQgX RcPkPhVeYa[ RaD\\E RbUZV RTDTTR[ RdFcM',
	    'Po]ElE RTKZK R_MjM RTRZR R^UlU R^ZlZ RTDZDZ[ R^P^[ R_H_M RePeZ R_HjHjM R^PlPl[ RW[Z[W[ RTDTSS[',
	    'Po_HjH RTKZK R_LjL R\\OlO RTRZR Rf[i[ RV[Z[ RTDZDZ[ R_D_L R_DjDjL R`Q\\U RbU]Z RgRcZ RbR`W R_SkSj[ RTDTSS[',
	    'Po_GlG RTKZK R_MjM R_PjP RTRZR RV[Z[ RTDZDZ[ R_I_P R_IjIjP RbQ\\W R`TdXk[ R`C^I R`SiSfW^[ RTDTSS[',
	    'Po^JjJ RTKZK R^NjN RTRZR R`[d[ RV[Z[ RTDZDZ[ R^F^N RdNd[ R^FjFjN RfTkY R`U\\Y RkQgT RcCcF RdOgV R\\RaR_X RTDTSS[',
	    'Po\\GlG R]JdJ RTKYK R\\MeM RTRYR RU[Y[ RTDYDYZ R]O]W R]OdOdW RlWl[ RbXe[ RiDkE R`W][ RhDhLhPiZ RTDTTR[ R`R`X',
	    'Po\\ElE RTKYK RbOfO RTRYR RbTfT R]ZkZ RV[Y[ RTDYDY[ R]J][ RbJbZ RgJgZ R]JkJk[ RdEcJ RTDTTR[',
	    'Po^IiI RTKZK R^NiN RTRZR R[[l[ RV[Z[ RTDZDZ[ R]R]Z R^E^N RaRaZ RfRfZ R^EiEiN R]RjRjZ RTDTTR[',
	    'Po\\GkG RTKYK R[KlK RTRYR R\\WgW RV[Y[ Rd[i[ RTDZDZ[ R_OgOfT RfKjO RaJ[P R^D_F RcE_M RhDgF R^Q^TjTi[ RTDTTR[',
	    'PobIjI RTKYK RbMjM RTRYR Rb[l[ RV[Y[ RTDYDY[ RbDbW RbDjDjM R[I^L RgSkW R\\D^F R[P^P^X[Z RkPgR ReNiV RgV`W RTDTSR[',
	    'Po\\FlF RTKZK R\\PlP RTRZR Rd[h[ RV[Z[ RTDZDZ[ R\\K\\N RdCdF RdMdP R\\KlKlN R`W\\[ R`G`J RhGgJ RaUiTh[ RbP`X RTDTSS[',
	    'Po[IlI RTKZK R[PlP RTRZR R\\SjS RV[Z[ RTEZEZ[ RaEa[ RfEf[ R\\LjLjS ReSkY RaT[Y RcD\\F RjDcF RTETSS[',
	    'PoRF^F R`GlG RWTgT RWWgW Rc[g[ RWQW[ RXCXF RaIaN RWQgQg[ ReJjO RaC_H RTORO RkJgK RVGTN RiHaI RfN_O RVJ\\I[OXO',
	    'PoTKZK R^NiN RTRZR R[[l[ RV[Z[ RTDZDZ[ R^Q^[ R^D^N RbQb[ RfQf[ R^DjDjN R^QjQj[ RdHgL RdFbK RTDTSS[',
	    'Po]JaJ RfJjJ RTKZK R]NbN RfNjN RTRZR RV[Z[ RTEZEZ[ R]F]N RdCdR RfFjFjN R_ScXj[ RbE]F R\\RjRfWaY\\[ RTETSR[',
	    'Po]GlG RTKZK R_NlN R`QjQ RTRZR R`ZjZ RW[Z[ RTEZEZ[ R`T`[ ReCeF ReHeS R`JjJjQ R`TjTj[ RTETSS[ R]G]S[[',
	    'Po]FkF R^IjI RTKZK R\\LlL RTRZR R]TkT RV[Z[ RTEZEZ[ RdTd[ RgVkZ RhQlU RdCdM_P R`V\\Z RgNaT R]OcR RTETSS[',
	    'PoTKYK RbMjM RTRYR RbWkW RbZlZ RV[Y[ RTDYDY[ RbFbW RfCeF RbFjFjL RbPkPkW R\\E_H R[M^M^X\\Z R^XeZ RTDTTR[',
	    'Po]ElE RTKZK R_LjL RTRZR RfSlS RaViV RV[Z[ Rh[l[ RTDZDZ[ R^O^[ R_H_L ReVeZ RfOfS R_HjHjL R^OlOl[ RcOaS_S RTDTSS[',
	    'Po\\JkJ RTKZK RTRZR R[ZlZ RV[Z[ RTDZDZ[ R]R]Z RbRbZ RfRfZ R]RjRjZ RfLkP RhDgH RbL]Q R_DaH RTDTSS[',
	    'Po\\FlF RTKZK R^MkM R^PkP RTRZR R\\TlT RV[Z[ Rd[g[ RTDZDZ[ R^I^R RdCdQ RhRh[ R^IkIkQ R`VbY RgDjD RTDTSS[',
	    'PoXShS RXWhW Rb[h[ RSLSP RXOXW RXOhOh[ RSLlLlP RkEiG RUESG R^E\\G RbE`G RXH^J ReGlJ RfDeGcI`J RVZT[ RYDWIRK',
	    'PoTKZK RTRZR R^RiR R^ViV RV[Z[ RTDZDZ[ R^N^[ RcGhLlN R]E`F RgIkJ RaI\\J RjDfF RbI`M\\N RdDbK R^NiNi[e[ RTDTSS[',
	    'PoTKZK R]NjN RTRZR R]TcT Re[l[ RV[Z[ RTDZDZ[ R]F]N R]P][ RfPfZ R]FjFjN RcCaF R`HeJ RfGdJ`K RkReU RcY\\Z RTDTSS[',
	    'PoTKZK R\\RkR RTRZR R\\UlU RW[Z[ RTEZEZ[ RhOlR RhFlJ RgK`Q RcFdI RbJ_McN RdVgYl[ R^E\\J RdSbX`Y\\[ RTETSS[ RjD^E',
	    'PoRElE RVJhJ RWQgQ RVVhV RVXhX Rc[h[ RSLSP RVSV[ RVGVJ RWNWQ R_C_E RWNgNfQ RVGhGhJ RVShSh[ RSLlLlP',
	    'Po`FlF RRF^F RXN\\N Re[i[ RUQU[ RaHaM RUQiQi[ RfJjN RaD^H R^QfT R_UgY RTNRN RjIgK R_P\\SXU R_T]WXY RXCWFUM RVI\\I\\N RiGaH RfM_M',
	    'PoTKZK R`OkO RTRZR R]UlU RV[Z[ RTDZDZ[ R]G]J R`K`U RfOfU R]GkGlJ RgXjY RdCeF RbX\\[ RTDTSS[ RiJ`K',
	    'Po]EkE RTKZK RaMiM RTRZR R_VdV RgVkV R]ZlZ RV[Z[ RTDZDZ[ R]E]Z R_P_V RaHaM R_PdPdV RgPgV RaHiHiM RgPkPkV RTDTSS[',
	    'Po_GlG RTKZK R_KjK R_NjN R\\QlQ RTRZR Rf[j[ RV[Z[ RTDZDZ[ R_I_N R_IjIjN R`R\\W RbV][ RfTdZ R_C]I R^UkTj[ RTDTSS[',
	    'Po]FlF RTKYK R]OkO RTRYR R_TdT Rf[l[ R_Q_[ RaFaO RfQf[ RfFfO R]JkJkO RlXl[ RcCdF RkSfU RTDYDY[V[ RdZ_[ RTDTTR[ R]F]S[[',
	    'Po_JjJ RTKZK R`RdR RTRZR R]ZlZ RV[Y[ RTDZDZ[ R]D][ R`M`S R`MdMdR RjUjX R]DlDl[ RhFjH RgScW RiMfU RdT_U RfGfNhW RTDTSS[',
	    'PoTKXK RaPjP RTRXR RaSjS R`VkV Ra[l[ RV[X[ RTDXDY[ RfNfY R[J]L R[D]F R]P]XZZ RdE`I RbGgLlN RcFjGeL`N R]PZP RTDTTR[',
	    'Po^IkI RTKZK R^NlN R^QkQ RTRZR R^UkU R^ZlZ RV[Z[ RTDZDZ[ R^D^I R_L_[ RdCdI ReMeY RkDkI R_L\\O ReJfM RTDTSS[',
	    'Po\\DlD RTKYK R]MkM R]QjQ RTRYR R[TlT R]H]M RaDaM RfDfM R]HkHkM RhVlZ R_V[Z RTDYDY[V[ RdTd[`[ RTDTTR[',
	    'PocJjJ RTKYK RTRYR RcTkT RbZlZ RTDYDY[ RcNcT RcDcJ RgJgZ RcDjDjJ RcNkNkT RjVl[ RU[X[U[ R^Z[Z RTDTTR[ R[D`D`J\\J\\Q`Q^Z',
	    'Po_EjE RXJ^JbKlL RXPjP RXRjR RZVhV Rd[h[ RXNXR RZTZ[ R_C_K RaNaR RZThTh[ RXNjNjR RUGkGjJ R^XYX R]X`XcX RUGTRSZ RdXhXeXbX^X',
	    'PoTKZK R_QiQ RTRZR R]UjU R\\ZlZ RV[Z[ RTDZDZ[ R]I]M R_M_Q RdQdZ RdCdI R_MiMiQ R]IkIkM R_D`G RiDhG RTDTSR[',
	    'Po\\FlF RTKZK R^NjN R^QjQ RTQZQ R\\UlU RW[Z[ RTEZEZ[ R^J^Q RaCaH RgCgH R^JjJjQ RTETSS[ RdRdUaY\\[ RdUhYl[',
	    'Po\\FkF RTKZK RTRZR RV[Z[ RTDZDZ[ R_SaU RiSgU RdCdLhPlR ReWkY RdFhKlL RdF_K\\L RdL`P\\R RbW\\Y RdQd[`[ RTDTSS[',
	    'Po\\FlF RTKZK R^KjK R^NjN RTSZS R\\UlU RW[Z[ Rd[g[ RTEZEZ[ R^H^N RdCdR RgSg[ R^HjHjN R_VbY R\\RjQlS RTETSS[',
	    'Po\\FlF RUKYK R`QgQ RTRYR R\\UlU RdSd[ R\\I^K RiNlQ RcDeF R_N\\Q RfJ`Q RkIhK RfNgR RcHaM RUDYDY[V[ ReL`L RUDUSSZ',
	    'PoTKYK RTRYR RV[Y[ RTDYDY[ R\\DbDbM ReDkDkM RgFeI RhIeM R_F\\I R`J\\N RdNfPlS RcN`Q[S RdR^U RgT^X RiWdY][ RTDTSR[',
	    'Po\\HkH RTKYK R[LlL RTRYR R^ShS R^VhV R^ZhZ RV[Y[ RTDYDY[ R^O^[ R]PiOi[ R]D^F RfLkQ RaL[R RhDgF RcF_O RcC`M RTDTTR[',
	    'Po[HlH RTKYK R]KjK R\\QjQ RTRYR R[TlT RV[Y[ RTDYDY[ R\\N\\Q R]E]K RcCcQ R]EjEjK R\\NjNjQ RaR^Wl[ RhUdY[[ RTDTSR[',
	    'PofKkK RTKZK R]MkM RTRZR R]RjR R]VjV R\\[l[ RV[Z[ RTDZDZ[ RdCcF RdSdZ RfFfJ R]F\\GlFkH RgOkS RaM_R RaFaJ]K RTDTSS[',
	    'Po]DkD RTKZK R_KbK RfKjK R^NbN RfNjN RTRZR R\\VlV R^ZjZ RV[Z[ RTDZDZ[ R]H\\K RdDdP R^RjRj[ R]HlHlK RTDTSS[',
	    'PoSE]E R_FlF Re[i[ RUPU[ RYCYN RUPiPi[ RiIdN RXHTM RSHZH\\J R]PfT R^UeX RhCjD R_P\\TXU R_T\\WXY R_IdIdMaM RfChL R^N^R R_F^M RlLlNjN',
	    'Po\\IkI RTKYK RdQkQ RTRYR RdWlW RdCdN RdSdW RiOi[ R^D`F RiDgG R]T`V[[ RdHfKlM R^O\\T R]RbR_X RcH`K[M RTDYDY[V[ RTDTTR[',
	    'Po]FkF R\\KlK R^QjQ R^TjT R^WkW R\\ZlZ RV[Z[ RTEZEZZ R^M^T RdMdZ R^MjMjT RVIXL RdCdF RYPVS RaGaK RhGfK RTETSS[',
	    'PoZFeF R[JdJ RTKYK R\\RcR RTRYR RU[Y[ RTDYDY[ R\\M\\R R]S]W R`C`J R\\McMdR RkDfJ RkLfQ RbSaW RkUgZd[ ReW[Y RTDTTR[',
	    'Po[GlG R]JdJ RTKYK R\\MeM RTRYR R\\RdR R\\TdT RV[Y[ RTDYDY[ R\\OdOdW R\\O\\WcXe[ RiDlE R^X\\[ RlWk[ RgCgNgQiZ RTDTSR[',
	    'Po\\FlF RTKYK RTRYR R^ZjZ RV[Y[ RTDYDY[ R\\J\\M R^U^[ R^M^R RdJdS R^UjUk[ R\\JlJlM RcCeF RhGgJ R`GaJ R^MjMjRgR RTDTTR[',
	    'Po\\GkG R_PiP R_TiT RV[Y[ R`[d[ RTDZDZ[ R_K_S RdTd[ R^MiLiS RVJXL RhVkZ R]I_K RfHlN RjHhK R_V[Z RaI\\N RcC`K RZPUR RTDTSS[',
	    'PobHjH R]HeH RaKiK R^KfK RTKYK R\\KcK RTRYR Rg[l[ RTDYDY[ R_Q_[ RhPhZ RbZ]Z RbP[Q RcEcMkNdP RV[Y[V[ RTDTSR[ RhC\\E RbS[T RkSdS RbV[W RlVdW',
	    'Po]IjI RTKZK R[LlL R^QjQ RTRZR R^TjT RV[Z[ RTDZDZ[ R]E]I RdCdL R^NjNjW R]EjEjI R^N^WhXl[ RaY\\[ RTDTSR[',
	    'Po]FjF R[IlI RTKYK R\\MbM ReMkM RdQlQ RTRYR R[TlT Rf[l[ RV[Y[ RTDYDY[ R_K_Q RcCcI RfTf[ RhKhQ RlXl[ R_Y[[ R`T_Y RbP[Q RTDTSR[',
	    'PoTKXK R_KgK RTRXR R^TiT R[ZlZ RU[X[ RTDXDY[ R^O^T R^OiOiT RfHlM RiDfG RkGhJ R\\G_I[N R`U`Y ReDhJ RgUeZ R\\EaE_K RTDTTR[',
	    'Po\\JlJ RTKZK RTRZR R^VjV R^ZjZ RV[Z[ RTDZDZ[ R^Q^[ R]EdEdP RdQdZ R^QjQj[ R_F`I RdJjP RdJ]Q RiFhI RTDTSS[ RiC`E',
	    'Po[GdG RTKYK RZLlL RTRYR R\\SbS R\\WbW R\\O\\Y R]H]L RaHaL R\\ObOcX R_C`G RiFjI RgUbZ RjOfV RlWk[ RTDYDY[U[ RfCfLhY RTDTTR[',
	    'Po^HkH RTKZK R^LjL R^OjO RTRZR R^TlT RW[Z[ RTEZEZ[ R_G_S RaWb[ ReFeS RfWf[ RiVk[ R`C]K ReDeI R^V\\[ RTETSS[',
	    'Po]GkG R^JjJ RTKZK R]MlM RTRZR R\\RlR R^ZjZ RV[Z[ RTDZDZ[ R^U^[ RdGdR R^UjUj[ R`CaF R`O`R RiNhR RhCgG RTDTSS[',
	    'PoTKZK R\\NlN RTRZR R\\TlT RV[Z[ RTDZDZ[ R_F_T RbGbT RfGfT R^HkGjT RbWb[ RfWf[ RiVl[ R_C\\K R^W\\[ RTDTSS[',
	    'Po\\FlF R]KkK RTKZK RTRZR R\\VlV RV[Z[ RTDZDZ[ RaCaH RdHdK RdSd[ RgCgH R`ObQ RgOkS RgNdR R`M^R RTDTSS[',
	    'Po]HcH RfHlH RTKYK R]LcL RfLlL R`OhO RTRYR RaShS R`WiW RV[Y[ RTDYDY[ R]D][ R]DcDcL RdOdW RfDfL RfDlDl[j[ RTDTTR[',
	    'PoTKXK RaKeK RaQeQ RTRXR RU[X[ RTDXDX[ RaFaY RaFeFeQ RgDg[ RbDdF R]DZK R^IZR R_P\\Y R^KZK RdTfY_Y RjMlRlViV RgElEjN R_QZQ RTDTTR[',
	    'Po`FlF RcIjI RTKYK RaLlL RbRjR RTRYR RbUjU RgXjX Ra[l[ RU[Y[ RTDYDY[ RbObX RfIgL RbOjOjX R[J]L R[D]F R]P]XZZ RdC`L R]PZP RTDTTR[',
	    'Po\\FkF R[JlJ RTKZK R]PjP RTRZR R]TjT R]WjW RW[Z[ RTEZEZ[ R]M]W R_H_L RdCdH RhHhL R]MjMjW RfXl[ RaX[[ RTETSS[',
	    'PoTFlF R_KjK R_NjN R_PkP RYUiU RYXiX Rd[i[ RYRY[ R[I[P R_I_P R`C`F RfHfP RYRiRi[ R\\GVM RaG\\L RTFTSS[',
	    'Po[FlF RTKYK R`MgM R]PjP RTRYR R^UiU R^XiX R[ZlZ R]H]O R^R^X R`K`M RcCcF R`KgKgM R^RiRiX R]HjHjO RTDYDY[V[ RTDTSR[',
	    'PoTKXK RTRXR RcUhU Ra[l[ RU[X[ RTDXDX[ R`M`X RcPcV RfHfM RcPhPhU RaDiDiM RZL]L]W`Z R[E^H R\\X[[ RaHfH RaDaMkMkXhX R\\LZL RTDTTR[',
	    'Po]IlI RTKYK RgLkL RaNjN R]QlQ RTRYR RaSjS RaUjU R`ZkZ RTDYDY[ R`W`[ RgHgL R`WkWk[ R_FfEeH R`C_G[J RcHaL_M RV[Y[V[ RTDTSR[ R^H]S\\Z',
	    'Po`IhI RTKZK R]QkQ RTRZR R_WiW R_ZiZ RV[Z[ RTDZDZ[ R]L]Q R_T_[ RdLdQ R_TjTj[ R]LkLkQ R`MaP RdChGlI RhMgP RcC_H\\I RTDTSS[',
	    'Po]IjI RTKYK R]LjL R\\OlO R_QjQ RTRYR R\\TlT RV[Y[ RTDYDY[ R]E]L R`T`Z RbCbL RfCfL R]EjEjL RjVfW ReUhZl[ R\\O\\V[[ RTDTSR[ RfY^Z',
	    'PoTG^G RUJ_J RUM_M RWUgU RWXgX Rc[g[ RWRW[ RXIXM R\\I\\M RTD^D^G RbDaH RWRgRg[ R\\N^P RXNVQ RbJeNjP RaJjJgMaP RbDhDhHlGlH RTDTJSP',
	    'Po\\FlF R\\LlL R\\QlQ RTRXR R^TjT RV[X[ RTDXDY[ RbHb[ RdCdF RfIf[ R^NjNjT ReTkZ RbT\\Z RiHdI^I RTKXKTK RTDTSR[ R\\F\\SZ[',
	    'Po`EkE RTH]H R_IlI R`LkL RUN]N RWTgT RWWgW Rc[g[ RUJUN RWPW[ RTD]E]H RUJ]J]N ReCeE ReIeO RWPgPg[ RbEbH RhEhH RTFSN',
	    'PoZGlG RTKXK RcMlM RcQkQ RTRXR RcUkU RcYlY RU[X[ RTDXDY[ RcCcG RdKd[ RhJhY R_W[[ R^I[M^O\\T R`L]S ReIaP R`S]Y R`TZU RTDTTR[ RhIhO',
	    'PocEjE RTKYK RcNjN R^PkP RTRYR RU[Y[ RTDYDY[ RcCcM RhTjYlY R]HkHjK RdQ_S RdT^V RdQfVcX^Z RjSfTfY Re[b[ RTDTTR[ R]H]S[Z RgJ_K',
	    'Po]IkI RTKYK RTRYR R]UgU R_YhY R\\YdY Rg[k[ RV[Y[ RTDYDY[ R]D]I R]P]U RaDaI RbNbX RfDfI R]PgPgU R]DkDkI RfWhY R_L[P R_I^NlMlUk[ RTDTSS[',
	    'Po]EkE R\\JlJ RTKZK R^PjP RTRZR R^SjS RV[Z[ RTDZDZ[ R^M^S RdCdF RaVa[hZh[ R^MjMjS RiVlY R`G`I RbTfV R^V\\Z RhGgI RTDTSS[',
	    'Po^GlG R^JkJ RTKYK R^MkM R^PlP RTRYR RbWfW RU[Y[ Rg[k[ RTDYDY[ R]S][ R^F^Q ReFeP RfSfW RfSkSl[ R_C]H R]SbSbW RTDTSS[',
	    'Po\\FlF RTKZK R^LjL R^OjO RTRZR R^YhY RV[Z[ Rf[j[ RTDZDZ[ R^S^Z R^I^O RaCaG RhChG R^IjIjO RcTfW R`P[U RcS`W R^RkSj[ RTDTSS[',
	    'Po_JgJ RTKYK RTRYR R]SbS ReSjS RV[Y[ RTDYDY[ R]M]S R]MbMbS ReMeS ReMjMjS RhXk[ R_WaY RcCiIlJ RfWc[ RcC\\J RgUeZ R_T]Z RTDTSR[',
	    'Po_HhH RTKZK R\\PbP RePkP RTRZR R\\SlS RV[Z[ RTDZDZ[ R\\K\\P R_D_H R\\KbKcP RdQd[ ReKeP R_DhDhH ReKkKkP RdSjY RdS]Z RTDTSS[',
	    'PoUJ_J RUL_L Re[i[ RUQU[ RXHXL RTH]H\\L RTD^D^G RUQjQj[ R\\N^O RXNVP RcLjO R^QfU R_UfX RaJjJgMaO R_Q\\TXU R^VXY RbDbH RbDhDhHkGkH RTDTISO',
	    'PoZGcG RdHlH R[KcK RTKYK RTRYR R[RbR R[N[R R_D_K R[NbNbR RhDhM RgVl[ RhUb[ R\\T\\W RePiY RcNkNgX RaT_X RcWZY RTDYDY[U[ RTDTTR[',
	    'Po[FlF RTKYK ReOiO RTRYR R]ViV RU[Y[ RTDYDY[ RcCcF RcKcO RfJfN R]RiQi[ RaGbI RiIlO R^I[O ReGdI RkIfJ R[I`J`N^N RTDTTR[ R]P]U\\Z',
	    'PoTKYK R[OkO RTRYR R[WlW RV[Y[ RTDYDY[ R\\DbDbL RcUc[ RdDkDkL R_QaT RgQjU RiDfH RiIfL RgQdT R`D]H R`I]L R_P]T RTDTSR[',
	    'Po_HjH RTKZK R^QjQ RTRZR R^TjT R^WjW R]F]H R^N^W RdHdM RdCdF R^NjNjW R]FlFlI RgYl[ R`J\\L RaY\\[ RhK]O RTDZDZ[V[ RTDTSS[',
	    'Po\\GlG R]PkP R]SkS R\\VlV RV[Y[ RTDYDZ[ R]J]P RaXa[ R]EdEdV R]JkJkP ReWf[ RVJXL RjWk[ R`L`N RgLgN R]W\\[ RYPUR RTDTSR[ RjDbD',
	    'Po]EkE R^JbJ RfJjJ RTKYK R]MbM RfMjM R\\PlP RTRYR RV[Y[ RTDYDY[ R\\H\\K R]T][ RbSb[ RdEdN RfTf[ R\\HlHlK R]TkTk[i[ RbPbV RTDTSR[',
	    'Po\\ElE R^KkK RTKZK R_MjM R_OjO RTRZR R_RkR RV[Z[ RTDZDZ[ R_I_S RaCaG ReIeR RgCgG R_J\\M R`WfYl[ R]UjUgWcY\\[ RTDTSS[',
	    'Po[FkF R[JkJ RTKXK R^MeM RTRXR R^TdT R^XeX RU[X[ RTDXDX[ R^M^Y R`C`H RbMbP RbTbX R^PdPdT RlXk[ RhHkI RhWd[ RkPgX RgIgPiZ RfFgM RTDTTR[ R[J[UZ[',
	    'Po\\DcD R\\JcJ RTKZK R^QjQ RTRZR R^TjT R^WjW R^N^W R_D_G R_J_L R\\GcGcJ R\\D\\L`MjNjW RfHkM RkGfL RfEhJ RfXl[ ReDlDiJ RaY\\[ RTEZEZ[W[ RTETSS[',
	    'Po]FlF RTKYK R]MkM R`PdP RTRYR RTDYDY[ R`M`T RaFaM RdCdF R]IkIkM RfFfUkTkU ReWf[ RhWl[ RaWa[ R]W\\[ RjOfQ RV[Y[V[ RdT^U RTDTSR[ R]F]P[V',
	    'Po[GaG RUGZG ReGiG RUJZJ RdJiJ R[K`K RRMlM RWTgT RWWgW Rc[g[ RUEUM RWPW[ R[D[M R[I`I`K R[DcDcM RVQhQg[ RdDiDiM ReMhQlR RYMVQRS RYDUE',
	    'Po^IjI RTKZK R^OjO RTRZR R^RjR R^UjU RW[Z[ RTEZEZ[ R\\F\\H R^M^U RaHaK RdCdF RfHfK R^MjMjU R\\FkFkH RfUf[lZl[ RgWiY RaU`Y\\[ RTETSS[',
	    'PoTKYK R\\PjP RTRYR RU[Y[ RTDYDY[ R\\J\\P R\\R\\Z RcRcZ R\\JjJjP R[F^H RbFdI RhFkI RkChF R^C[F RdCbF ReSgT ReVgW R^TaT R^VaV RlYl[ R_LgN RfL^N RaYZZ RhYaZ RiSj[ RTDTTR[',
	    'Po[FlF R`IgI RTKYK R[N^N RTRYR RaSeS RU[Y[ RTDYDY[ R`C`I RaMaS RaWb[ RaMeMeS RgCgI RjWk[ RkMhP ReWf[ R]W[[ RiKiTlSlT R^K^TZS RTDTTR[',
	    'PocEkE RbJhJ R_JfJ RTKYK RcLiL R_QiQ RTRYR R_SiS R]ZlZ RU[Y[ RTDYDY[ R^V^Z R_N_S RbVbZ RcCcG RdNdS RfVfZ R_NiNiS R^VjVjZ R\\GkGkJ RcHcL RTDTTR[ R\\G\\T[[',
	    'Po]DkD R^JbJ RfJiJ RTKYK RfMjM R^MbM R^RkR RTRYR R^TjT R^WjW R^ZlZ RW[Y[ RTDYDY[ R\\G\\J R_P_[ RdDdN ReOeZ R\\GlGlJ R_P[T RTDTSR[',
	    'Po\\FbF RdIkI RTKYK RiNlN RTRYR R^P^W RhChH R^PjPjW ReWiY R\\D\\G RfIeM R[IaIaL_M RbX\\[ R_C^I\\M RTDYDY[V[ ReDeGiFiL RcRcX RTDTTR[',
	    'PoTEkE RUHjH RVMiM R[RaR RTSXS R[TaT RcThT RTVXV Ri[l[ RV[X[ RTETH RVJUM RTPXPX[ R[P[T R^P^[ R[PaPaT RVJhJhM RcPiPj[ RlXl[ RZV_VaX R^VZZ RTPTVSZ RePeUeY',
	    'Po]GbG ReGkG R]JbJ ReJkJ RTKYK R]MbM ReMkM R]QkQ RTRYR R^TjT R^WjW R^ZlZ RV[Y[ RTEYEY[ R]D]M R^P^[ R]DbDbM RdOdZ ReDeM ReDkDkM R_N[S RTETSS[',
	    'Po[FlF R^KlK RTKXK R[O^O RTRXR R`XfX RU[X[ RTDXDX[ R[K[P R^K^X R`C`I R`N`Y R`NdNcQ R`UdUcX R`QeQfU RlXk[ RiHlI RjPfZ RkNhW R[SZZ RfCgIhRiZ RTDTTR[ RZS^S][',
	    'Po\\DbD R[FcF R\\HbH RdKlK RRKYK R\\KbK R\\PbP Rd[i[ RURU[ RVJVP R\\M\\P R\\MbMbP RiJiP RURiRi[ RSFUH RdFgH RjFeJ RUCTG RXFSJ RgCfG RYMYO RkMkO R^ReV R_VfY RSLRP ReLdP R^SYV R_U]XXZ',
	    'PoRE_E RaGlG RUG[G RaIkI RSL]L RaLkL RRN^N RaOlO Re[j[ RTRT[ RUCUG R[C[G RbEbP RgDgO RTRjRj[ R^ReU R^VfY RYGXNSP R_RXU R_T]WXY RSJWJ RSHSI]H]JYJ R[P]P',
	    'Po\\FbF ReFkF RTKYK RiMlM R^QjQ RTRYR R^TjT R^WjW RV[Y[ RTDYDY[ R^O^W RdIjIiM R^OjOjW RhChGiK RfXl[ R]D\\G R_C^I]M ReDeG RfIeM R\\IbIaM_M RaX[[ RTDTTR[',
	    'PoREkE RRSjS RRZlZ RRER[ R^E^K R^S^Z RRKjKjT',
	    'PoRE^E RRR]R RRX^X RRERZ RXEXK RXRXX RRK]K]R ReUl[ RfS_[ RaKfW RjJeV RaHdS R_FkFhR',
	    'PoRE_E RRR^R RRY`Y RRERZ RYEYK RYRYX RRK^K^R RhWl[ RbU][ RfQjY ReDdP`X ReLbV ReHgU ReJcT',
	    'PoaElE RSL\\L RSS\\S RaSkS RR[\\[ Ra[l[ RSLSS RWEWL RWSW[ RRE]E][ RaEa[ RfEfL RfSf[ RaLkLkS',
	    'PoXGlG R[KbK R[TbT R[XcX RXGXW R[K[Y R^K_O R_T_X R[ObObS RgUa[ RgDkE RjMeW RTGTMXM RlVk[i[ ReCeNfX RTRSY RRRXQV[',
	    'PoRE\\E R_GlG R`OjO RRR[R RRY\\Y R^ZcZ RfZlZ RRERZ RWEWK RWRWX RRK[K[R R^R^[ R`J`O R^RcRc[ RfRf[ R`JjJjO RfRlRl[ R`C]J',
	    'PoaDlD RTH]H RTK]K RaKkK RSN]N RaNlN RSQkQ RTHTK RXDXH RXKXN RSD^D^N RaDaN RcTc[ RfDfH RfKfN RaHkHkK RdSlV R\\OYSRV R[TYYU[',
	    'PoRMlM RRSlS RRYlY RRGR[ RRGlGl[ R^C\\G',
	    'PoRM^M RRS^S RRY^Y Rc[k[ RRGR[ RRG^G^[ RcDc[ RWCWG RlVk[',
	    'PoVIhI RVLhL RVPhP RRSlS RVFVO R_Q_[ RVFhFhO R_C_F R`ScWlZ R]SZWRZ',
	    'PoVIhI RVMhM RVQhQ RRTlT RVFVP RVFhFhP R_TcXfZl[ R_Q]UZYR[ RaD_E',
	    'PoVHhH RVJhJ RTQ`P_[ RVEhEhM RXT\\Y RfTlZ ReVaZ RXURZ RfQcX RXRVX ReO_P RVEVMiNaP',
	    'PoVHhH RVKhK RVNhN RTQ[Q RbQjQ RST[T RbTkT RRWlW RVEVN R_P_[ RVEhEhN',
	    'PoWHhH RWKhK RWNhN RSQkQ Ra[l[ RWEWN RbQbZ RWEhEhN RlXlZ R_C_F RZQUU[X RXSRV RWT_T[XT[ RjSaV',
	    'PoWGhG RWJhJ RWLhL RbRjR RSTlT R`[f[ RWEWL R_L_O RbObR RWEhEhL R[WhWf[ R\\UYYR[ R[QTR RSOSPlOlQ',
	    'PoTI]I R_KlK RTM]M RTP]P RSS^S RfUjU Rd[l[ RTFTP RXPX[ RTF]F]P RdNd[ RcNjNjT RlWl[ RXS[V RXCWF RYSSY RbFhFfJ RcC`J`S_Z',
	    'PoSFlF RSMiM RUSjS RR[l[ R_O_[ RfJkO R\\FXM',
	    'PoRE^E RTM^M RSR^R RXNXX ReUk[ R\\I]M RWETM RcC_N R_WRY RaIkIeUaY^[ RaIcPeUk[',
	    'PoRE^E RTL]L RSR]R RaZjZ RXNXX RaQa[ R_RkRj[ RZI]L RcKjQ RbGeN RWETL RcC`J RbGjGgKbP^R R]WRX',
	    'PoRE]E R]HlH RTL\\L RRMZM RSR\\R RaSlS Rd[g[ RWNWX R`K`[ RgPg[ RYI\\K RcLkLgP RWETL RcE^O R\\WRX',
	    'PoTF`F RRJlJ RSMaM RYR`R RTR\\R RTU`U RZCZJ R]OaR RgXj[ RfDjG RfUa[ RYMTR RjLeX RlVk[ RdCdKfX RZS[YSZ R_YVZ',
	    'PoRE^E R`ElE RTM]M RRMZM RaMkM R_NgN RSS]S R_TkT R^[l[ RXNXY ReOe[ RZI]L RjKlO RhIjL RWETM RdEaM R]XRZ',
	    'PoRF\\F R]HkH RRM[M RSR[R RWNWW RhDkF RgVb[ RYH[M RjNfX RVFTM R[VRX R]NcNcV_V RfCfMfPhZ R]H]S\\Z RlVk[i[',
	    'PoRElE RTHjH RVNhN RVRhR RUXiX RR[l[ RSPSS RVJVN R_C_H R_U_[ RVJhJhN RSPkPkS R[RYU RTVfUjV',
	    'PoRE\\E R]FkF R^JkJ R\\MlM RSQ[Q R^TjT RWNWX RdQd[ RYI\\L RdTiY RfMlR RaM\\S ReJhP RcH_P RdT_Y\\Z RVETL RdDaN RgP_Q R\\WRY R[KRL',
	    'PoRO\\O RaOlO RRYlY RRGR[ RaFlFl[ R]DRG',
	    'PocLiL RUT^T RcTlT RUZlZ RUOU[ RbOlOl[ R\\DVJRL RZFkFiK R_NUO',
	    'PoTL[L RbLjL RbRjR RTR[R RTFTR RbFjFjR R_C_PaUdXj[ R]TZXR[ R[ETF R_L^T',
	    'PoUEjE RRJlJ RTT\\T RbTjT RTZjZ RTOT[ R_E_Z RbNjNj[ R\\MTO',
	    'PoWF^F RUT]T RaTjT RUZjZ RUNU[ RaNjNj[ RfFlL R^G_L RVFTL R^MUN R`EZE RgD]E',
	    'PoUJ]J RaJjJ RUNjN RRTlT RUEUN RePe[ RaEjEjN RYVVYR[ R]DUE RYQYW',
	    'PoSFkF RUJiJ RRMlM RXV]V RaVfV RXZfZ RXRX[ R`RfRf[ RVQRS R[LTS RbLhRlS R^EZN R]QWR',
	    'PoUH^H RbHkH RULkL RXPkP Rf[j[ RUEUL RZVZZ RbEjEjL RdUgX R_V`Y RUVSZ RXLXTlTk[ R^DUE',
	    'PoUI]I RbIjI RUNjN Rf[k[ RVEVN RaEjEjN ReThW RZNST RaUbY RVUTZ R[U\\Z R]DUE RVRlRk[',
	    'PoTG^G RbGlG RTJlJ RTPlP RTSlS Rd[j[ RTETJ RTMTS RTMlMlS RbElElJ RSVlVk[ R`M`R^V[YR[ R]DTE',
	    'Po[FcF RUJYJ ReJjJ R[LcL R[OdO RUPYP RePjP RRUlU RUEUU R`I`S ReEjEjU RcWk[ R^D\\L R[WS[ RZDTE',
	    'Po]GcG RUJZJ RdJiJ RUOZO RdOiO RRUlU RUFUT R]N]T R]C]KbKbT RdEiEiT RcWl[ R[WR[ RZDUE',
	    'Po]HbH RTJYJ RfJjJ RUOYO ReOjO R]QaQ RRUlU RUEUT R[E[T R]K]Q R]KaKaQ R[EdEdT ReEjEjT RcWl[ R[WR[ RYDTE',
	    'Po]FbF RUHZH RdHiH RVKZK RdKhK RROlO RYSeS RUWjW RVEVO R]K]O R_Q_[ R]D]IaIaO RdEiEiO RdOhSlT RZOVSRT RZDUD',
	    'PoRElE RVJjJ RVLiL RVOiO RVRkR RUW]W RaWjW RUZjZ RUTU[ RWIWR RZCZG R`H`R RdCdG RaTjTj[ RXHRM R]SUT',
	    'Po]FaF RUGYG ReGiG RUJYJ ReJiJ R^KaK RWTgT RWWgW RR[l[ RSMSQ RWPW[ R[D[M R^H^K R^H`H`K R[DcDcM RWPgPg[ ReDiDiM RUETMkMkQ RYDTE R`H]H',
	    'PoRLlL RVZhZ RVRV[ RTFZF_F`R RVRhRh[ RhD\\F',
	    'PoXJfJ RTOkO RWZhZ RWSW[ R_J_S RWShSh[ R_CdHlK R^CYHRL',
	    'PoXKfK RSPkP RWZgZ RWSW[ R_H_P RWSgSg[ R_CdHlK R^CYIRL',
	    'PoRK`K RTY^Y RTRT[ RZDZR RTR^R^Z RcEcU RlCl[ RlS`U R^CRE',
	    'PoRK]K R`MlM RSY\\Y RSRS[ RXEXR RSR\\R\\Z R`F`Z RlWk[ RkD`F RfY^Z R\\DRE RgEgNgPhZ',
	    'PoRK\\K R_MjM R]SlS RSY[Y Rf[j[ RSRS[ RXEXR RSR[R[Z R_G_[ RdCdS R_GjGj[ R\\DRE',
	    'PoUJ]J RSN^N RTY]Y Ra[e[ RTST[ RYJYS RTS]S]Z ReMe[ RYC^H R`EkEeI RXDRJ RaHgJ R_MlMjQ',
	    'Po_IjI RRK\\K R_MjM RSY[Y Rf[j[ RSQS[ RXDXQ RSQ[Q[Z R_D_M R_DjDkM RaO\\S RcR]X RfVa[ RhQdY R[DRE R_QlQj[',
	    'Po\\JkJ RRK[K RSY[Y RSRS[ RWDWR RSR[R[Z RfKlQ RaK\\P RfRgW RiRkX RcE_N R^R]X RbOb[_[ R[DRE RiC]E',
	    'PoSE[E RRK\\K RSY[Y RSQS[ RWEWQ RSQ[Q[Z RfVj[ R_Q`T R_E`H RjEhH RjQiU ReSgY RdIkL RdIaM^N RdVaY][ ReCcK ReOcX',
	    'Po^GlG RTJ\\J RSN]N R_PkP R_UkU RTY\\Y RTST[ RXJXS RTS\\S\\Z R_K_[ ReCe[ RXC]H RWERJ RhDlE R_KlKk[h[',
	    'Po^GlG RTK\\K RSP]P R_PkP R_UkU RTY\\Y RTST[ RXHXP RTS\\S\\Z R_K_[ ReCe[ RXC]H RXCRJ RhDlE R_KlKk[h[',
	    'PoUJ\\J RSN]N RaPjP RaYkY RTY\\Y RTST[ RXJXS RTS\\S\\Z R_G_J RaJa[ ReCeG RaJjJjP RaTkTk[ R_GlGlJ RXC]H RWERJ',
	    'PoSGkG RWMhM RRS^S R`SlS RTZ]Z RaZjZ RTVT[ RWJWN RYPYV RTV]V][ RUE_E_J RaVa[ RfPfV RWJgJgN RaVjVj[ RgC`D R\\PTQ RjPbQ',
	    'Po]IlI RRK\\K RgLkL RaNjN R^QlQ RaSjS RaUjU RSY[Y RaZkZ RSRS[ RWDWR RSR[R[Z RaWa[ RgIgL RaWkWk[ R`EgFcIbL`L R[DRE RaE]I^S\\[',
	    'Po`IkI R`SlS RgCg[ RYUS[ RTMZP RWFRP R]MXW RWCTM RVH^HZT R`L_T',
	    'Po_PlP R^VlV RSJSN RaPaV RgLg[ RSJkJkN RWFXI RhGdJ RXNRT RTE_F_I RWP^OZV RURXT[TWYR[ RhDbD^E',
	    'PoRHlH RVTjT RRXlX RSNSQ RaRa[ RUEgEgH RSNkNkQ RUKXL R\\K`L RXIUK R_I\\K R]NYQ RdKgL RVRePhQ R]C[H RVTVX RgIdK',
	    'PoVGjG RRJlJ RSOkO RVS]S R_RkR R^WlW RWEWO R\\H\\O RaRaW RbHbO RfPf[ RgHgO RUTYV RXDSH RXPRV R\\TXYS[',
	    'PoROlO Rb[h[ R_Q_Y RWGhGh[ R]IaM R^C^G RWGWQVW',
	    'PoRO\\O RY[\\[ Re[j[ RXCXG RTG]G]Z RXIYM RXRZU RcR`Z R_FlFlSj[ RTGTTSZ ReFdKbV',
	    'Po`FkF RRP^P R_ZlZ RY[\\[ RXQXW RTG\\G\\[ ReFeZ RWJYM RWCWG RTGTTSZ',
	    'PoRP^P R`YlY RXQXW R`J`[ RfDfX RlJl[ RXIXM RXCWG RTG\\G\\[X[ RTGTTSZ',
	    'PoRO[O RX[[[ RTG[G[Z RWRYU RWIXM RcEeJ R`LdUhYl[ RXCVG RiKeT`Y\\[ R^FbS RkEgR RTGTTSZ',
	    'PoRO[O RX[[[ Rh[l[ RTG[G[Z R_EhEi[ RaLfQ RWRYU RWIXM RlWl[ RWCWG RTGTTS[ R_E_S^Z',
	    'PoRO[O R^PkP R_[k[ RX[[[ RTG[G[[ R_E_Z RdEdP R^EkEkR RlWk[ RWRXU RWIXM RWCUGTTS[',
	    'Po^KlK RRO[O RX[[[ RWCWG RTG[G[Z RhWl[ RbUfY RWRYU RbV\\[ RWIXM RePiY RcP_Y ReCeLhV RdKaW RTGTTSZ RdHbT',
	    'PoRO[O RX[[[ Ri[l[ RTG[G[Z R`GiGiZ RlWl[ RWRYU RWIXM RcMeS RWCWG RdDbG RTGTTSZ R`G`S^Y',
	    'Po`EkE RRP]P RX[[[ RXQXW RTG\\G\\[ ReUk[ RhUb[ RWIXM RbNfW RjNfW RWCWG RbLdT R`LkLhU RTGTTSZ R`E`R^Z',
	    'PoRP\\P RdZkZ RaZhZ R_ZeZ RX[[[ RXQXW RWCVG\\G\\[ RWJXM RhSl[ RbEhEkN RaG_O RdN`Z RTGTTSZ',
	    'Po]HlH RRP]P Rh[l[ RX[[[ RXQXW RTG[G[[ ReCeH R`MhMiZ RlWlZ RWJXM RWCWG RTGTTS[ R`M`T_Y',
	    'Po^HlH RRP^P RX[\\[ Re[i[ RXQXW RTG\\G\\[ ReCeH RcHcOjNjY RXIXM RbS_[ RWCWG RTGTTSZ RcKaW',
	    'PoiLlL RX[[[ RXQXW RTG\\G\\[ RaDhDhL RlIlL RWJXM RaPbTfXl[ R_PjPhTdX^[ RWCWG RTGTTS[ RaE`L RXORP R[OTP',
	    'Po_LdL RRO[O RfZlZ RW[Z[ RTG[G[[ R_D_Z RfDfZ RlXl[ RWRXV RWJXM RlKfM RdX^Z RWCUGTTS[',
	    'Po_ElE RRP]P R`QkQ RX[\\[ RXQXW RTG\\G\\[ R`JkJkS RXJXM RWCWG R`J`T_[ RTGTTSZ',
	    'PoRO\\O R_RlR R_I_T ReCe[ R_IlIlT RXIXM RXRYU RWCWG RTG\\G\\[X[ RTGTTSZ',
	    'PoRP[P RfZkZ RX[[[ RWCVG[G[[ R_E_R RgSgZ R_EjEjR RcU^[ RWRYV RWIXM ReIdRaX RlVlZ RTGTTSZ RdMcV',
	    'PoRP\\P RcThT Ra[k[ RX[[[ RXCUG\\G\\[ RbMbZ RgHlM RkVkZ RcG^M RXRYU RXIXM ReDhI ReCbI RTGTTSZ RaMiMhT',
	    'Po`LiL RRPXP RfXjX RX[[[ RWQWW RTG[G[[ RcPc[ R^PjPjX ReElL RcF]L RWJXM RWCWG RcCbH RTGTTS[ R[OTO',
	    'PoRP^P R`QlQ R`ZlZ RXQXW R`I`[ RfCfZ R`IlIl[ RXJXM RXCVG\\G\\[X[ RTGUSSZ',
	    'PoaIlI ReNkN RRP^P ReTkT RXQXW ReHe[ RXJYM RcC^M RWCVG\\G\\[Y[ RTGTTSZ',
	    'PoRP]P Rb[k[ RX[[[ RXQXW RTG[G[[ R^G^K RbLbZ ReCeG R^GlGlK RlWl[ RWJXM RWCWG RjOaS RTGTTS[',
	    'PoRP_P RaQlQ RaZlZ RY[][ RYQYW RUG]G][ RaHa[ RaHlHl[ RXJYM RXCWG RfCfI RUGUSSZ',
	    'Po]HlH RRP]P R]ZkY RX[[[ RXQXW RTG[G[[ RjWl[ RWJXM RWCWG ReCeGaO RTGTTS[ RiL_Z RaOeR',
	    'PoRP]P R_TeT Re[j[ RX[[[ RXQXW RTG[G[[ R_K_V R_KeKeT R^EkEj[ RWJXM RWCWG RTGTTS[',
	    'PoRP]P RaZjZ RY[\\[ RXQXW RTG\\G\\[ RaPa[ RaPjPj[ RXJYM RgDlN RXCWG RcD`N RTGTTS[',
	    'Po`KlK RRP^P R`RlR RX[\\[ RXQXW RUG\\G\\[ R`E`S RfEf[ R`ElElS RXIYM RXCXG RUGUSSZ',
	    'PoeFlF RRO\\O R`QkQ RWCWG ReCeK R`KkKkS RWIXM RXRYU RTG\\G\\ZX[ RTGTTSZ R`K`S^Z',
	    'Po^JkJ RRP]P R]RlR RX[[[ RXQXW RTG[G[[ RaCaR RhChR RgVkY RaW][ RWJXM RWCWG RTGTTS[',
	    'PoTO[O R`QkQ R]WlW RW[[[ RWQWW RTG[G[[ R`Q`W ReNe[ RbD]J RWJXM RaGeLlN RWCWG RaFiGdL]O RTGTTS[',
	    'Po]FlF RRO[O RgTkT R^ZkZ RW[[[ RWCUG[G[[ R^K^[ RgFgT R^KkKk[ RWRXU RWIXM RTGTTS[ RcFbObT',
	    'Po]GkG R`MhM RROZO R]QjQ RW[Z[ Rc[g[ RTGZGZ[ ReGlM RbH\\N RWRXU RWIXM RdCaJ R`Q_UiUh[ RWCUGTTS[',
	    'PoRP]P R^SlS RX[[[ Ra[e[ RXQXW RTG[G[[ ReQe[ R_MjMeQ RWJXM RdGeJ RiFkJ RWCWG R`E^K RjC`E RTGTTS[',
	    'Po_IkI R]NlN RRO[O R_ZkZ RX[[[ RTG[G[Z R_R_[ RdCdN R_RkRk[ RWRYU RWIXM RWCWG R_D^K RTGTTS[',
	    'PoRO[O R_QjQ R_TjT R]WkW RW[Z[ RTG[G[[ ReNe[ RbE]J RWRXU RWIXM RaGeLlO RaFjFeL]O RWCUGTTS[',
	    'Po_KkK RRP]P R_PkP R_UkU R][l[ RX[\\[ RXQXW RWCVG\\G\\[ R_E_P ReEe[ R_EkEkP RXJXM RTGTTSZ',
	    'PoRP]P R`QjQ R`UjU Rg[j[ RX[[[ RXQXW RWCVG\\G\\[ R`L`[ ReCeL R`LjLj[ RWJYM RhEkH RaE_J RTGTTSZ',
	    'Po`KiK RSP\\P R^PkP RX[[[ Ra[d[ RXQXW RTG[G[[ RdKd[ ReDlK RdCbG]K RWJXM RiSkY R`S]Y RWCWG RTGTTS[',
	    'Po`LjL RRP]P R`QjQ RX[\\[ RXQXW RTG\\G\\[ R`G`Z R`GjGjQ RfVk[ RjTfU RWJXM RdQhY ReCeG RWCWG RgY]Z RTGTTSZ',
	    'PocMlM RRP\\P RcVkV RW[Z[ RWQWW RTGZGZ[ RhEhV RWJWM RWCVG RjDcF R\\E`E]NaN_X RTGTTS[ R^Uc[m[',
	    'Po]GkG R^MkM RROZO RW[Z[ Ri[l[ RTG[G[[ RdPdZ RiPi[ RlXl[ RcCdGaJ^M RWRXU RWIXM RiJkO RWCUGTTS[ R_P_T^Y',
	    'Po_JkJ RRP\\P Rd[k[ RX[[[ RXQXW RTG[G[[ ReMeZ R_EkEkJ RkXk[ RWJXM RWCWG RiL`M RTGTTS[ R_E_S][ RjP`Q RlT`V',
	    'Po_JkJ R_NkN RRO[O RiYlY R^Q^Y R_F_N ReNe[ R_FkFkN R^QlQlX RXRYU ReCdF RXIXM RTG[G[[X[ RTGTTSZ RWCWG',
	    'Po\\JlJ RRP\\P RaPdP R\\[l[ RWQWW R^S^Z RbSbZ RdHdP RfSfZ R^SjSjZ R_EiEeH RWJWM RTGZGZ[W[ RWCWG RTGTUS[',
	    'PoRP[P RW[[[ Ra[i[ RTG[G[[ RbTb[ RhXi[ RdSeW RbJeL RdEkL RcG]M RWRXU RWIXM RiUkZ R_NiNgT RXCVG RdCaI R^U]Z RTGTTSZ',
	    'Po_JkJ R_NlN RRO[O R`ZkZ RW[Z[ RTG[G[[ R_D`T`[ RfJfT R`TkTk[ R_DkDkJ RWRXU RWIXM RWCUGTTS[ R^N]Z',
	    'Po_JkJ R_NkN RRP]P R_RkR R_VkV R^ZlZ RX[[[ RXQXW RTG[G[[ R_F_N ReReZ R_FkFkN RWJXM RWCWG ReCdF RTGTTSZ',
	    'Po^HlH RRP^P R`QjQ R`UjU R`ZjZ RXQXW R`M`[ R`MjMj[ RXJYM RaDbG ReHcM RiChG RXCVG\\G\\[Y[ RUGTTSZ',
	    'Po^KjK RRO[O RX[[[ RTG[G[Z R^E^K RkEkK RhMkQ ReGhJ RaM^Q RbP]U RgDbI RWRXU RWIXM R`SdXk[ R`EeF RWCWG R`SiSeX^[ RTGTTS[',
	    'Po_EeE R_MlM RUO\\O RRP[P RaUlU RW[Z[ RWQWW RTG[G[[ RaPa[ RdPd[ RhPh[ R_HlHlM RWJWM RaPlPl[j[ RWCWG RTGTTS[ R_E_S][ RjDbE',
	    'Po`HjH R`LjL RRO[O R_SkS R_VkV R_ZkZ RX[[[ RTG[G[[ R^D^L R_O_[ R_OkOk[ R^DlDlL RWRXU RWIXM RXCVG RTGTTS[',
	    'Po]GlG RdLiL R_OkO RRO[O R]SlS RW[Z[ RTG[G[[ R_D_O RdCdK ReOe[ RiCiK RfTlZ RcT]Z RWRXU RWJXM RWCUGTTS[',
	    'Po^FlF R`KkK RcOiO RROZO R`RjR RW[Z[ RTGZG[[ RcHcO RiHiO RdCeF RWRXU RWIXM RbTfXlZ RjTeX_[ RWCUGTTS[ R^F^S]Z',
	    'Po^FlF RRO[O R^PlP RX[[[ Re[i[ RWCUG[G[Z R^K^N ReMfP ReCeF R^KlKlN RaX^[ RWRYU RWIXM RbGbJ RiGgK RcPaX RbUjUi[ RTGTTSZ',
	    'Po^GkG R_KkK R^PlP RbTjT R_ZlZ RX[[[ RXQXW RTG[G[[ ReGeO ReTeZ RWJXM RaDbG RWCWG RhCgF RaP^Z RTGTTS[ R[OSP',
	    'Po`JhJ R\\ZlZ RW[Z[ RWQWW RTGZGZ[ R^T^Z RbTbZ RfTfZ R^TiTiZ RhPlT RWJWM RbNgNjS RbEgEiI R`E^J R`N]T RWCWG RTGTTS[ RXORP R\\OTP',
	    'Po^NbN RgNkN RRP]P R^RjR RW[[[ RWQWW RTG[G[[ R^F^N ReCeQ RgFkFkN RWJWM R`SdXk[ RjSeX][ RbD^F R^JbJ^J RWCWG RhJkJgJ RTGTTS[',
	    'PoaIhI R_NjN RRP]P R_RjR RaZkZ RX[[[ RWQWW RTG[G[[ R_K`Ua[ R_KjKjR RaUkUk[ ReCjI RdC]J RWJXM RWCWG RTGTTS[ R_P^Y',
	    'Po]FlF R^MkM RRP\\P R^PkP R_VjV R_ZjZ RW[Z[ RWQWW RTGZG[[ R^I^P R_S_[ RbCbP RfCfP R_SjSj[ R^IkIkP RWJWM RWCVG RTGTTS[',
	    'Po\\HlH R_KjK RRO[O R^QkQ R\\TlT RW[Z[ RTG[G[[ R^M^Q R_E_K RdCdP R_EjEjK R^MkMjQ RWRXU RWJXM RbQ_Wk[ RhUeY\\[ RWCUGTTS[',
	    'Po]GlG RRO\\O RgZjZ RW[Z[ RWCWG RWQWW RWIWM RTGZGZ[ R_R_Z R`C`J RdNd[ RfCfK R_RjRjY R^N^OkNkQ RjCiLlKlL RcCcKfKbK RTGTTS[',
	    'Po^EkE R_KbK RfKiK R^NbN RfNjN RRP\\P R]VlV R_ZjZ RWQWW R]H]L RdEdP R_RjRj[ R]HkHkL RWJWM RTGZGZ[W[ RWCWG RTGTUS[',
	    'Po^GdG RfGlG RROZO RW[Z[ Rf[l[ RTGZGZ[ RfKf[ RlXl[ R`FbJ RiFjJ RiMhP RaW^[ RWRXU RWIXL R^M_P RfSkU RWCWG R_C\\J RgCdJ RbS\\U RTGTTS[ RbKbTaX',
	    'Po]FkF R\\KlK RRP\\P R^QjQ R^TjT R^WkW R\\[l[ RWQWW R^M^T RdCdF RdMdZ R^MjMjT RWJWM RaGaJ RhGgJ RTGZGZ[W[ RWCWG RTGTUS[',
	    'PoaKhK RRP\\P R_TiT R]ZlZ RW[[[ RWQWW RTG[G[[ R_O_T R_OiOiT RgHlM RiDgG RkHhI R^GaI]M RWIXM RbVbY RfDiJ R^EcE`K RgVgY RWCWG RTGTTS[',
	    'Po`OjO RRO[O R^VkV R^ZkZ RW[[[ RWCVG[G[Z R^G]K R^R^[ R`K`O ReCeG ReReZ R`KjKjO R^RkRl[ R]GlGlK R`DaF RjDiF RWRYU RWIXM RTGTTSZ',
	    'Po^FkF R]NlN RRP\\P RbXgX R^ZkZ RWQWW R^R^[ RdCdN RbTgTgX RhJkM R^RkRk[ R`JbL RWJWM RiHgL R`H^M RbTbX RTGZGZ[W[ RWCWG RTGTUS[',
	    'PodEjE RbKiK R`KfK RdMkM R^RlR RaUjU Re[i[ RWQWW RaOaT RdCdM RaOjOjT RWJWM R^HkHkJ R_WjWj[ RdY_[ RTGZGZ[W[ ReOeTdY RRPZO\\O RWCWG RTGTUS[ R^H^T\\[',
	    'Po]FkF R^IjI R\\MlM R\\SlS RWQWW RaOa[ RdFdM RlYl[ RgXj[ RiOkP R`CaF RhCgF RWJWM RjUfYcZ RcO]O RTGZGZ[W[ R][a[][ RdV\\W RfOgY RWCWG RTGTUS[ RXORP R\\OTP',
	    'Po]IcI R\\LlL RROZO R]PcP R]ScS RV[Z[ RTGZGZ[ R]L]X R]E]I RcLc[ R]EcEcI RlWl[ RVIWM RWRWU RhEjH RjQfZ RjNhX RWCVG RdV[X RgCgNiZ RTGTTS[',
	    'Po]DdD RfHlH R]LcL RfNkN RRP[P R]PdP R[[l[ RV[Y[ RWQWW RVJVM RTGZGZ[ R]D]P R^S^Z RbSbZ R]HcHcL RfSfZ R^SjSjZ RWCVG R`LaP R`D`H RgCeK RTGTUS[',
	    'Po\\ElE R^KkK R_MjM RTO\\O R_OjO RRPYP R_RlR RW[Z[ RWQWW RTGZGZ[ R_I_R RaCaG ReIeR RgCgG RWIWM R`WfYl[ R\\UjUgWcY\\[ RWCWG RTGTTS[',
	    'Po\\FlF R`LiL R^OkO RRPZP RW[Z[ RWQWW RTGZGZ[ R]I]L RaCaH RgCgH R]IkIkL RgUlZ RjRgT RWJWM ReOhW ReO]S RcS]V RcReVbW\\Z RWCWG RTGTUS[ ReWd[`[',
	    'Po\\FlF RROZO R^WjW RW[Z[ RTGZGZ[ RdIdO RcCdF RfJfO R^RjQj[ RfGdJ RVJWM RWRWU RiIlO RWCVG RkIfJ R^J]O RaO_O R^P^U]Z RTGTTS[ R\\JaJaO RiNeO',
	    'Po^KjK RRP\\P R_WjW R_ZjZ RWQWW R_T_[ RbPbR ReHeN R_TjTj[ R]IjHkN R_G^NjOlR R_P]R RaD\\I R`EgEdH RWJWM RfPfR RTGZGZ[W[ RWCWG RTGTUS[',
	    'PodEjE R`JhJ RdLjL RRP\\P RaQjQ RaSjS R^ZlZ RWQWW R`V`Z RaNaS RdVdZ RdCdL ReNfS RgVgZ RaNjNjS R`VkVkZ R^HlHlJ RWJWM RTGZGZ[W[ RWCWG RTGTUS[ R^H^T][',
	    'PoeOlO RROZO R^QhQ R]VjV RV[Z[ RTGZGZ[ R]R]V R\\GjGjJ R\\SjSjW ReVe[lZl[ R_EhEeG R^K_O\\S RVIWM RVRXU RgXjY RWCVG RcVaY\\[ ReKeO RcSbY R`C]G]JjKeL R^LcM]O RTGTUR[',
	    'Po]GcG ReGkG R]JcJ ReJkJ R]McM ReMkM R]PcP RePlP RRP[P RWQWW RVJVM R^F^O RaEaO ReEeO RiEiO R_ScXj[ R^C[J R\\SiSeW][ RWCUGZGZ[V[ RTGTUR[',
	    'PoUJhJ RUPhP RUEUZ RUEhEhP R^P_TcXj[ RjQaV R\\YR[ R^YT[ R`YWZ',
	    'PoVKhK RVPhP RVFVZ R^C_F RVFhFhP RjRaV R]P_TbXfZl[ R`YR[',
	    'Po^JjJ R^OjO R^E^[ R^EjEjO ReUjZ RWTRY RVM[X RTJWP RcOgW R[KUW RkReU RSG\\GXS RdY]Z',
	    'PoRF_F RUI\\I RaJjJ RaOjO RTO^O RSR^R RRU_U RTLTO RUCUI R\\C\\I RTL^L^O RaDaZ RaDjDjO RgVl[ RYIXVTZ RkQhT ReQiY RXW]Z ReOhW RfY_Z',
	    'PoYEdE RVQjQ RV[k[ RVIVZ R_J_Q RVJjJjS R[CRL RcF_J RlWk[',
	    'PoRH\\H RSM[M R^RkR RRT\\T R_[k[ RWCW[ R_K_Z RdKdR R^KkKkR R`GhFfK RlWk[ RaC_I',
	    'PoSM^M RaRkR Rb[k[ RZCZ[ RRG^G^L RaJaZ RgKgR R`KkKkS RlWk[ RdC`L RcGhFfM R^W[W RVCVRUX RRLRQ^Q^W',
	    'PoRQ^Q R`RkR Ra[k[ RSI\\I[[ RaKaZ RfJfR R`KkKkR RlWl[ RUDVG R\\DZI RcC`K RbGhFfL RUIUSTY',
	    'PoSI^I RSL^L RRO_O RaRkR RTV]V RS[Y[ Ra[k[ RSFSL RTQTU RWCWL RZCZL RTQ]Q]U RSF^F^L RaJaZ RgJgR R`KkKkS RlWl[ RVWVZ RdC`L RcGhGfL R^ZW[ R[W[ZU[',
	    'PoUHWH R[H^H RTJWJ R[J^J RSM_M RRO`O RbSkS RTV^V RV[_[ RR[[[ Rc[k[ RSDSM RUEWEVK RYCYM R[E]E\\K R_D_M RbJbZ RfKfR RaKkKkS RlXl[ RVWVZ RdCaL R[W\\Y RdFiFfM RTRTV RTR^R^V RWETE',
	    'PoSR]R RbRlR RSHST R]H]T RbHbT RgDg[ RlHlT RWVR[ RXDXQWW',
	    'PoRPlP RZIZZ RdIdZ',
	    'PoRGlG RV[k[ RZCZJ ReCeJ RlWk[ RZTUY RUMgMbO`Q^R[T',
	    'PoRGlG R`[g[ RZCZJ RdCdJ R\\UYYR[ RSNiNiSh[ R^J\\U',
	    'PoRF^F R`FlF RRMlM RY[_[ RXCXJ R_M_[ ReCeJ',
	    'PoRFlF Rf[l[ RZCZJ RdCdJ RTNfNf[ R[UWYR[ RlWl[ R\\J[U',
	    'PoRFlF RZCZJ RdCdJ RXMZQ_VdYl[ RfLdR_VZYR[',
	    'PoSFlF Rc[i[ RZCZI ReCeI R[PYVVYR[ R\\LZU RTLdLdQkQj[',
	    'PoSGlG RSMjM Ra[h[ RZCZJ RdCdJ R\\TXXR[ R^M[V RjNiZ',
	    'PoRFlF Re[l[ RZCZI RdCdI RXLeLfZ RWVS[ RlVl[ RXLXSWW',
	    'PoRGlG RcWhW RZCZJ R\\M\\[ RdCdJ RSMiMiW',
	    'PoRFlF Re[l[ RZCZI RdCdI RXKeKf[ RlWl[ R\\PaT RWVR[ RXKXRWW',
	    'PoRFkF Rg[l[ RZCZI RdCdI RTMgMg[ RVQbW R]O[VXYR[ RlWl[ R^J\\T',
	    'PoRF^F R`FlF RbPjP R\\PdP RZPaP RVQ^Q RTQ\\Q RRQYQ RZCZH RdCdH RcKhM R^NaVeYi[ R]K`T RlXk[i[',
	    'PoRFlF RYCYI RdCdI R]MaQ RWLYQ_VdYl[ RULhLfPaUZYR[',
	    'PoRGlG ReXjX RTNTX RZCZJ R_J_[ RdCdJ RTNjNjX',
	    'PoRHZH RdHlH RUPjP RRUlU RZDZP R_C_[ RdDdP',
	    'PoRFlF RTLjL RRRlR RZCZJ R_L_[ RdCdJ',
	    'PoRFlF RTL[L RRQlQ RZCZJ R`K`[ RdCdI R^KYL RhJ\\L',
	    'PoRGlG RTKjK RRRlR RY[_[ RZCZJ R_K_[ RdCdJ',
	    'PoRFlF RVLiL RRPlP R_[e[ RZCZJ RdCdJ RYPWTgUe[',
	    'PoRFlF RWMjM Ra[h[ RZCZI RdCdI RZQ_V RXJWNRQ RjNi[',
	    'PoRFlF RVPiP Ra[g[ RZCZI RdCdI RTKiKiO RVPTUjTh[',
	    'PoRGlG RUQjQ RR[l[ RZCZK R_L_[ RdCdK',
	    'PoRFlF RRMlM RZCZJ R_M_[ RdCdJ R_RjU',
	    'PoRFlF RURhR RU[k[ RURUZ RYCYI RdCdI RTKhKhR RlWk[',
	    'PoRFlF RUNkN RV[j[ RUNUZ RZCZJ R_J_N RdCdJ',
	    'PoRFlF RRRlR RY[_[ RZCZI R_O_[ RdCdI RWKhK_O',
	    'PoUIjI RSV]V RaVlV RSPSW RUDUK RXOXX R]P]W RaPaW RfOf[ RjDjK RlPlW R_C_J]LYM RWYR[',
	    'PoRFlF RYCYI RdCdI RZNhMfP`V R]JXPSQ RZQ]RaU[YS[',
	    'PoRGlG RSPlP RZCZJ RdCdJ R`PbUeYk[ R]TYXR[ R_J]T',
	    'PoRFlF RZTdT RWTaT RZCZI RdCdI RdL_N R]IWO`OUT RhPbV]XS[',
	    'PoRFlF RUP^P Ra[k[ RUJUZ RZCZJ RbJb[ RdCdJ RlWk[ RjMaR R_XR[',
	    'PoRFlF RULjL RRRlR RZCZI RdCdI R`TdXj[ R^TZXR[ R_I_P^U',
	    'PoRFlF RSN\\N RUUiU RUPUX RZCZJ RdCdJ RiPiW R_J_[kZk[ R_MXN RiK[M',
	    'PoRFlF RRPlP RUTiT RZCZI RdCdI RUKiKiV R_K_U\\XYZT[',
	    'PoRFlF RTLiL RRQlQ R`[k[ RZCZI RaQaZ RdCdI RlXk[ R]TZXT[ R_L]U',
	    'PoRGlG RZCZJ R_I_M RdCdJ RWVS[ RSMiMaSUYa[k[',
	    'PoRF^F R`FlF RVLkL RXNhN RhZk[ RYCYH ReCeH RlXl[ RXJRP RTQfQhZ',
	    'PoRFlF RZCZH RdCdH RXJeJePkOkP RWORQ RWR[V_Xh[ RXJWO RURhRdV_XWZ R^Xl[ R`XR[',
	    'PoRGlG RRNlN RZCZJ R_J_M RdCdJ RXNYQ^VdYl[ RfNeQ`VZYR[',
	    'PoSFkF RZCZI RdCdI RZKTR R_PaUdXk[ RYMjMgQ R_P]UZXS[ R_M`T',
	    'PoRFlF R\\NkN RZZlZ RXNX[ RZCZI RdCdI RXNRT RYKWP',
	    'PoRFlF RRKlK RZCZI R`N`[ RdCdI RbPkT RaK]PRU',
	    'PoRFlF Rc[k[ RU[Y[ RZCZI RZOZ[ RdKdZ RdCdI RlWk[ RSL_K\\NZO R\\RRS R_RTS',
	    'PoSFkF RZCZI RdCdI RdPd[ RYVT[ R_IbLlQ R^IZMRQ RYPYW',
	    'PoSFlF RVTjT RZCZH RdCdH RVNjNjV RhI\\KVKVTS[',
	    'PoRFlF RRQlQ RZ[_[ RZCZI R_Q_[ RdCdI RZLcQ RVJhJ_N RjRfT',
	    'PoRFlF RXQiQ RZCZI RdCdI RVWR[ RZPaWfYl[ RTKdKcP RiRdW_YX[ RYKWV',
	    'PoSFkF RYOeO RZCZI RdCdI RaKlP RVTgTc[ R^I[LRP R^I`I',
	    'PoRFlF RVQlQ RVKVZ RZCZI RdCdI RlXk[ RbQdWgZj[ RaMdW RVL\\KaKcU RhJ^K R`YR[',
	    'PoRFlF RUKiK RRQlQ Rc[k[ RZCZI RcQc[ RdCdI RlXk[ RZVWYR[ R[QYX',
	    'PoSFkF RWQgQ R`[e[ RZCZH RdCdH R[JdJkQ RYKSQ R]Q[WYYT[ RgRf[',
	    'PoRFlF RTRjR RU[j[ RUKUZ RYCYI R_K_R RdCdI RTKjKjT RlWj[',
	    'PoRFlF Rd[j[ RTNT[ RYCYH RdCdI RTNjNj[ R_MaRcThV R_M]RZUVV RXJ_J^Q',
	    'PoSGkG RZ[e[ RZOZ[ RZCZK RdCdK ReWe[ RiRkX R\\LbO RURRY',
	    'PoRFlF RSLlL RZCZI R_H_P RdCdI RWP\\VbYj[ RTPhPcU^XT[',
	    'PoSFlF Ra[k[ RXNX[ R[C[I RaJaZ RdCdI RlWk[ RYMRS RZJXO RjM`R',
	    'PoRFkF RX[g[ RXPX[ RZCZI RdCdI RhWh[ R_IbLlQ R^I[MRQ RePXT',
	    'PoRFlF RRMlM R`[f[ RZCZI R_I_M RdCdI R[RhRf[ R[MYVVYR[',
	    'PoSGlG Rc[h[ R[C[J ReCeJ R\\QUY RaU\\Z RXJWMRR R^M[S ReM`X RWMkMi[',
	    'PoRF^F RaFlF RSSlS RYCYI R_I_T RfCfI RVMgMgS RaUgXkY R^T[XWZR[',
	    'PoRFlF Rb[h[ RZCZI RdCdI RWKRP RZRVV RWIUN R\\NZS RVLjLjQh[ R^VVW R`UYV RaQcSdV\\V',
	    'PoRFlF R_QiQ RR[l[ RWPW[ RZCZJ R_K_[ RdCdJ',
	    'PoRFlF RULiL RRQlQ R^ZiZ R\\ZfZ RWZcZ RSZ`Z RZCZI RdCdI RdTk[ R[QWZ',
	    'PoSFlF R[KbK RVK_K RVPlP RZCZI RcPc[ RdCdI RVKVRUX RhI`J',
	    'PoRFlF RRQlQ RZCZI RdCdI RaSeXl[ R]TYXS[ RTLZL_K]U RhJ]K',
	    'PoWKfK ReOjO RWWfW Re[j[ RWSWX RWHWL R^F^L R^Q^X ReHeK RZCSJ RZNRU RfSeW R\\MTO R\\YT[ RWFkFjO RWQlQk[',
	    'PoRFlF RdOkO RaPgP R^PeP RXQ^Q RSQYQ RfTlT RdTjT R^UeU R\\UbU RXV^V RSVYV R_[k[ RZCZI R_K_[ RdCdI RlXk[ R`PZP R[LUL RgJYL R[UUV',
	    'PoRFlF RTKkK RRRlR R^[d[ RYKYQ RZCZI RdCdI ReKe[ RbSYWRY',
	    'PoRFlF RRLlL RdYiY RUPUY RYCYI R_I_[ RdCdI RUPiPiX',
	    'PoSElE Rd[j[ RWLW[ RZCZH RdCdH R]WZ[ RWMRR RXIVN R[JgJfPkPkTj[ R`J^V',
	    'PoRFlF RRMlM Re[l[ RZCZI RdCdI RYPePe[ RlWk[ R_I`M RYPXWVYR[',
	    'PoSFlF RZCZI RdCdI RfUl[ RYQ^V RVWR[ RbV][ RdPhX RYQUY RdP`Y RYLVW ReMbW',
	    'PoRGlG RZCZJ RdCdJ RhOeR R_MbUeXl[ RWMUR R]TYXR[ R_J]T',
	    'PoRG^G R`GlG RRMlM RdCdJ RWUR[ R[MVW RYQ^VcYk[ RZCZJXU RYRgRbW^YW[',
	    'PoRFlF RRNlN RYCYI R^N^[ RdCdI R^I_L R`RhU',
	    'PoRFlF RZCZI RdMdV RdCdI RTL_W RZSSZ R^JYT RjJj[f[',
	    'PoRFlF Rd[i[ RZCZI RWJVW\\V\\W RdCdI R`V[[ RbL`W R]LkLkTi[ R]NRP',
	    'PoRFlF RTKkK RUOiO ReZiZ RZCZI R_I_[ RdCdI RRTkTiY',
	    'PoSFlF RUJlJ RXPjP Rc[h[ RZCZI RdCdI R^U[YV[ RjQh[ RUJUSTY R`L^V',
	    'PoRFlF RTKkK RcWjW RT[k[ RTKT[ RZCZI RdKdV RdCdI R[TWX RjSjW R\\K[U',
	    'PoSFlF RUKkK RaUgU R[ZjZ RZCZI R[O[Z ReCeI RkVkZ RZOhOgU RUKUSTY',
	    'PoRFlF Ra[k[ RVKVU RZCZI RbTbZ RdCdI RVKhKhU RlXk[ R^T[XT[ R_O^V',
	    'PoRFlF RRQlQ RXJX[ RZCZH RdCdH R_RcWkZ RgJbL\\N RaYW[',
	    'PoSFkF R_WdW RY[g[ RYPY[ RZCZI RdCdI RhWg[ R`KkP R_I\\LRP RXPePdV',
	    'PoRGlG RR[l[ RYCYK ReCeK R^J_N RSOSS RSOkOkS',
	    'PoRFlF R`[e[ RZCZJ RdCdJ RjRkV RVQUV R[UWYS[ R]K[U RTMfMfUe[',
	    'PoRFlF RVPiP ReYjY RZCZI R_J_[ RdCdI RTJiJiO R^T\\VRZ RUTkTjY RVOUU',
	    'PoSFlF RWL_L RfTjT Rb[l[ RZCZI RbLbZ RdCdI RaLjLkT RXYS[ R\\TVZ RlXk[ RVNRR RUP\\S RXJUP R^MZV',
	    'PoRFlF RVOhO RRUlU Rc[h[ RVJV[ RZCZH R_H_U RdCdH RVJhJh[',
	    'PoSFkF RYOeO RcYhY RZCZI R]S][ RdCdI RTShShY R_HbKlP R^H[LRP',
	    'PoRFlF RRPjP RVZhZ RVSV[ RZCZI RdCdI RVShSh[ R\\JWO RcKjO',
	    'PoRFlF RcQhQ RVZiZ RVSV[ RZCZH RdCdH RVSiSi[ RSJiKhQ R]LYPSR',
	    'PoRG^G R`GlG RUTjT RUZjZ RUNU[ RZCZJ R_J_Z RdCdJ RUNjNj[',
	    'PoRGlG RUSiS RUZiZ RUMU[ RZCZJ R_M_Z RdCdJ RUMiMi[',
	    'PoRFlF RZVdV Re[j[ RTKT[ RZCZI RZPZW RdCdI RZPdPdV RTKjKj[',
	    'PoRFlF RSNkN RR[l[ RZCZJ R_J_N RdCdJ RYPZY RfPc[',
	    'PoRFlF RVPiP RVViV Rd[i[ RZCZI R_K_[ RdCdI RVKiKi[ RVKVRUX',
	    'PoRFlF RRLlL RVWaW Rb[g[ RVPVX RZCZI RVPaPaV RdCdI RgLg[',
	    'PoRFlF RVPhP RVUhU RVZhZ RVKV[ RZCZI RdCdI RVKhKh[',
	    'PoRFlF RSQ[Q Rb[k[ RYCYI R[L[[ RcLc[ RdCdI RkWk[ RjObS R[WRX',
	    'PoRFlF RWTbT RdWhW RW[k[ RWOWZ RZCZI RWObObT RdCdI RWKRP RlWk[ RWLiLiQhW',
	    'PoSFlF RWWbW Rb[h[ RWPWX RZCZI RWPbPbV ReCeI RYJRQ RWLkLjTi[',
	    'PoRFlF RUOjO RUSlS RUJUZ RZCZI RdCdI RUJjJjO R`PbVeYi[ RlXk[h[ R[ZT[ R^YWZ RaYYZ',
	    'PoRFlF RVJVX RZCZI RdCdI R\\KaP ReTl[ RhMeTaXZ[ RhJfT R^URY',
	    'PoRFlF RWPgP RWZiZ RWJW[ RZCZI RdCdI RWJgJgP RWTiTi[',
	    'PoRFlF RUJkJ RUUiU RUZlZ RUJU[ RZCZH RdCdH RUOiOiU',
	    'PoSGlG RSLkL RR[l[ RZCZJ R`N`Y RdCdJ RbQlU RaL]QRV',
	    'PoSFlF RRMlM RZCZI RdCdI R]IZPTV RYRiRiZYZYR',
	    'PoRFlF RRMlM RVZiZ RVRV[ RYCYI R_J_R RdCdI RVRiRi[',
	    'PoRFlF RRPlP RZ[_[ RZCZH R_H_K R_P_[ RdCdH RTKSO RTKjKkO',
	    'PoSFlF RVOjO R[[j[ RZCZH R\\P\\Z RdCdH RVJjJjO RkXj[ RiR[U RVJVRTY',
	    'PoRGlG RVShS RVZhZ RVMV[ RZCZJ RdCdJ RVMhMi[ R^I]M',
	    'PoRFlF RRJlJ Re[j[ RTNT[ RZCZH RdCdH RTNjNj[ R_MaRcUhW R^Q[UVV R_J^R',
	    'PoRFlF R^MlM RUZiZ RURU[ RYCYI R^I^R RdCdI RURiRi[',
	    'PoRFlF RTZjZ RTJT[ RZCZI RdCdI RTJjJj[ R_PhY R^SZWVX R_L]T',
	    'PoRFlF RWX]X RUY[Y RRYXY Re[j[ RZCZI RdCdI R[U]X RTOXS RbW^[ RWLTO RXUTY RXJVN R[NYR R_NkNkUj[ RdLcV',
	    'PoSFlF R[ZkZ RWNW[ RZCZI R[O[Z RcKcZ RdCdI RkOk[ RXMRR RYJVO',
	    'PoSFlF RSLkL RZCZI R_I_[ RdCdI R_LhU RXVgUlV R^MWURW',
	    'PoRFlF RSO\\O RU[Z[ RZCZH RSJ\\J\\O RdCdH RgRk[ RdN^Z RdJaV RSORT\\T[[ RgY_Z RjXbY',
	    'PoSFkF RRSlS RVLVS RZCZI RdCdI RVLhLhS R`TdXgZl[ R^T[XWZR[ R_I_Q^U',
	    'PoRF^F R`FlF RYMlM R^QjQ R^UjU RXCXI R^M^[ ReCeI RZKSR',
	    'PoSFlF RdCdH R[TbV RYKSN RZC[HXL]OcRlT RZKfJbN\\RRT RgYX[',
	    'PoRFlF RWQgQ RWVgV RR[l[ RWKW[ RZCZI RdCdI RWKgKh[',
	    'PoRF^F R`FlF RVNiN RRSlS RZCZI RdCdI RWJTQ R_I_S`UcXgZl[ R^T[XWZR[ R_N^U',
	    'PoSFkF R[[_[ RZCZI R_N_[ RdCdI RdRjX RYSTX R_IcMlQ R^IZMRQ',
	    'PoRFlF RRMlM RXSfS RXZfZ RXJX[ RZCZI RdCdI RfJf[',
	    'PoRF^F R`FlF RRSlS RY[_[ RZCZI RTK_K_[ RdCdH RXMZQ RfLdQ RhJ_K',
	    'PoRFlF RTKjK RRTlT RZCZI R_K_[ RdCdI ReNiR RZMUR',
	    'PoRFlF RRQlQ RUXlX RZCZH RdCdH R\\LbN R\\SbU RWKUY RWKhKhSf[b[',
	    'PoRFlF RZNlN Rb[g[ RWMW[ RZCZI RdCdI RgJg[ RWMRR R^R_V RXJVO',
	    'PoRF^F R`FlF RVOjO RVUiU RR[l[ RZCZI R_K_[ ReCeI RVOSR RWKUQ',
	    'PoRFlF RZCZH RdCdH RcTf[ RgSk[ RVKVRSZ RfJiW RVMUX RgI^J RVK\\K_K^YWZ R`YZY ReX^Y',
	    'PoSGlG RZ[f[ R[N[[ RZCZJ RdCdJ RgPiRlW R\\LaM RVPSW RfM^USZ RfVe[',
	    'PoRFlF RTLjL RSQlQ RZCZI R_I_[ RdCdI R_QeWlZ R_QYWRZ',
	    'PoRElE RUPjP RfYjY RcIc[ RdCdH RSKjKjO RZC[H[UXYS[ RTTkTjY RTOTT R[OZW',
	    'PoRFlF RVQhQ RTYjY RTST[ RVKVQ RZCZJ R_J_Y RdCdJ RhKhQ RjSj[',
	    'PoSFlF RUMlM RZCZI RdCdI RgJjL RUMUSTY RhR\\[ R_JaQcXfZk[mX',
	    'PoRFlF RdSiS R][k[ RYCYI R]K]Z RdCdI R]KiKiS RYTS[ RlWk[ RTKYM RRPXQ',
	    'PoSFlF RbYkY RY[][ RZCZI RbLb[ RdCdI RbLkLk[ RXIXOVW RSM^M^U][',
	    'PoRFlF R[[`[ RZCZH R`Q`[ RdCdH RRQlQgV RUJhJ_N R^QZURX RZMcO',
	    'PoSFlF RfWkW RUKUV RZCZH RbKb[ RdCdH RbKkKkW R\\M\\VYYU[ R^IUL R\\URW',
	    'PoRFlF RRMlM RZCZI RdCdH RXUR[ RbJgK R\\M^ScWk[ R\\MWW ReNcT_XY[ R\\IYU',
	    'PoRFlF RZP_P Rc[k[ RUNU[ RZCZH RZKZY RcKcZ RdCdH RlWlZ RkNbR R_XR[',
	    'PoRFlF RRLlL RTQkQ RZCZI R_H_[ RdCdI R_PeVlY R_PYVRY',
	    'PoRGlG RWQjQ RR[l[ RWMW[ RZCZI RdCdI RdQd[ RfKXM',
	    'PoRFlF RUK[K RUPkP RTZbZ RUKUW RZCZI RdCdH RlXk[ RaQdWfZj[ R^JXK R`JbV RgI\\K R`VRX',
	    'PoSFlF RZPlP R[[l[ RWMW[ RZCZI RcJc[ RdCdI RYJWNRR',
	    'PodFlF RWWbW Rb[h[ RWPWY RRF[E[I RWPbPbW RdCdI RXKRQ RWLjLjTi[',
	    'PoRFlF RVUhU RR[l[ RZCZH R_R_[ RdCdH RWJZN_PhR RTJhJeM`PWR R^PlS RaPRS',
	    'PoRFlF RRNlN Ra[l[ RZCZI RaNaZ RdCdI RZUTZ RlXl[ R\\NXW RgQdTZY RbKgL R]KZU',
	    'PoRFlF Rb[h[ RZCZI RdCdI RbSZZ RXJRQ R\\OTW R]L[Q RdL`U RXQdT RWMkMh[',
	    'PoRFlF RcQfQ RRWgW Rd[i[ RZCZI RdCdI R^I\\K R[N`O RWKWSkSj[ RWKgKfQ',
	    'PoSFlF RWL_L RZCZI RbKb[ ReCeH RgQjT RVMRR RbKjKgQ RXIUO R_M[V RWQ\\SWYSZ RjWeX',
	    'PoRFlF RSMSR RZCZJ RdCdJ RSMkMkR RdTk[ R]I`L RYUR[ RdReV RYRWW',
	    'PoRGlG RUSiS RR[l[ RSLSP RZCZJ R_N_[ RdCdJ RSLkLkP',
	    'PoRFlF RVOgO RTTdT Ri[k[ RSJSN RZCZH R\\N\\[ RdCdH RSJkJkN RlXk[ RgPhZ',
	    'PoRFlF RWZgZ RWSW[ RdCdH RWTgTg[ RYKSN RZC[HYK[NaQkS RZJfJbN[QRT',
	    'PoRFlF RYZjZ RZSZ[ RZCZI RdCdI RWTjTj[ RWM]O R\\JSN RZKfJaOZSRU',
	    'PoRElE RcOhO RX[\\[ Rf[j[ RZCZH RRR]R]Y RdCdH R`RkRkZ RVWR[ RcW_[ RdSbY RWRUY RUIiIhO R_I[NTP',
	    'PoRF^F R`FlF RRLlL RSS^S RYCYH ReCeH RdVh[ RYNXWT[ RaPeY RfJjK RiQcW[[ RaLdW RlWk[i[ R`JcU',
	    'PoRFlF RSQ]Q RfWkW RSLSX RZCZI RaKa[ RdCdI RaKkKkW R\\JSK R^VRX',
	    'PoRFlF RVNhN RVRhR RVJVZ RZCZH RdCdH RVJhJhR RjSbW R_ScXgZl[ R`YT[',
	    'PoRFlF RRJlJ RbTjT RTZjZ RTOT[ RZCZI RcJcT RdCdI RTOjOj[ R[J[RWV',
	    'PoRFlF RUJkJ R_UhU RUZlZ RUJU[ RZCZH RZMZW R_U_Z RdCdH R_J_OhOiU',
	    'PoRFlF RUJjJ RUUhU RUZlZ RUJU[ RZCZH R_J_O R_U_Z RdCdH RUOhOhT',
	    'PoRFlF RRNlN RUQiQ RTTjT RRXlX RZCZH R_H_[ RdCdH RUJiJiQ',
	    'PoSF^F RaFkF RZNkN RWMW[ RZCZH RbJb[ ReCeH RdQlX RdNeR R`RYX RXLRR RaN_S RbM`R',
	    'PoRFlF RRTlT RZ[_[ RSJSN RZCZH R_H_K R_R_[ RdCdH RWNgN_R RSJkJkN',
	    'PoSElE RSJ`J Rf[j[ RZCZH RcKcV RdCdH RjIj[ RWNRS RYJVO_O[V RUQXS\\UWYT[',
	    'PoRElE R[NhN RRUlU RZCZH R[I[N RdCdH RUJUQhQhU R[IhIhN R`UcYk[ R_Q]VZYR[',
	    'PoRFlF RZCZH RdCdH R[M]O R_TaW R]JTN RbQWV R[KeJbM[PSS RaQjQfU`XV[',
	    'PoRFlF RUMiM RSQkQ RUVjV RR[l[ RZCZI R_J_Q R_R_[ RdCdI',
	    'PoRF]F R`FkF RSLkL RfUiU RUOUU RYCYI R_I_[ ReCeI RUOiOiU R_RbVlZ R_R[VRZ',
	    'PoRFlF RUSiS RYZkZ RTZgZ RULUS RZCZI R_I_Z RdCdI RULiLiS RgVl[',
	    'PoRFlF RSMZM RZCZI RdCdI R]MkMiR R^I[R RYURW RcMcQ`W^YX[ RcQeVhYl[',
	    'PoRFlF RVK`K RVOlO RYZiZ RYSY[ RZCZH ReCeH RYSiSi[ RVKVRTY RiJaJ',
	    'PoRFlF RSRlR Rb[l[ RZCZI R_J_R RcRc[ RdCdI RVLYO RhLeO RlXk[ R[SZWXYS[',
	    'PoRFlF R]MlM R][l[ RZCZI R]M]Z RdCdI RdJdM RYTS[ RTKYM RRPWQ',
	    'PoSFlF RSKlK R[RkR RZZlZ RXPX[ RcMcZ RdCdI RZLST RZC[HYN',
	    'PoRFlF RRKlK RZCZI R_H_L RdCdI RcNkQ RYORR RYQ^VcYl[ RdQ`V[YR[',
	    'PoRFlF R_LiL RRQlQ R[[_[ RZCZI R_H_[ RdCdI RfTkZ RXTSY',
	    'PoSFlF RZOlO RWMW[ R[C[I RdCdI ReUjZ RfKiL RXLRR RcJcPfX RYJWO RcO`V]YY[ RbLaU',
	    'PoRFlF RVOiO RVSiS Rd[l[ RVJVS RZCZI RdCdI RVJiJiS R_H_U[YR[ RXUZW^Zh[',
	    'PoSFlF RVMjM RRRlR RZCZH R_I_[ RdCdH R_QdVlZ RXIUN R^RYWRZ',
	    'PoRFlF RSZ^Z R`ZkZ RZCZI RdCdI R[U^[ RSOXTSZ RhPeT R`OeT`Z R[QXS RiVk[ RXJUP ReJcO',
	    'PoRFlF R[LkL RYZlZ RZCZI RcLcZ RdCdI RYUS[ RTJYM RRPWR',
	    'PoRFlF RZUdU RTZjZ RTJT[ RZCZH RZNZU RZNdNdU RdCdH RTJjJj[',
	    'PoRElE RWPgP RTZjZ RTJT[ RZCZH RdCdH RTJjJj[ R`ScVgX R^R[VWW R_M^T',
	    'PoRFkF RVRhR RZCZI R_N_[ RdCdI RdTjZ RYUTZ R_HcLlP R^HZMRQ',
	    'PoSFlF RdOkO RWMW[ RZCZI RdCdI RlXk[ RWNRR RaQdY RZP^PaOcOiPcVfZj[ RXJVO ReVZ[ ReKiL R`JbU',
	    'PoRFlF RSKkK RXOgO RXSgS R_XlX R\\XiX RYXgX RRXbX RXKXX RZCZH RdCdH RgKg[',
	    'PoRFlF RRM^M RaYkY RZCZI RaLaZ RdCdI RaLkLkZ RTT]X RYJSU R^O[VXYS[',
	    'PoRElE RRKlK RTQiQ Rb[k[ RZCZH R_H_K RcPcZ RdCdH RhOjR RlWl[ RcMhN RZVWYR[ R\\KZPYX',
	    'PoaJlJ RSJ]J RRV]V RaVlV RRQRV RSESJ R]Q]V RaPaV RaEaJ RfCfM RfOf[ RlPlV RXNXWS[ RXJTM RlEkJ R]E]J RXDWK',
	    'PoRFlF RXNfN RZWdW Re[j[ RTJT[ RZRZY RZCZH RdCdH RZRdRdW RTJjJj[',
	    'PoSFlF R[TkT RWMW[ RZCZI R[M[U RcJc[ ReCeI R[MkMkU RXMRS RYJWO',
	    'PoRFlF RTLjL RRRlR RZCZI RdCdI ReLe[ RYVT[ RZLZRYW',
	    'PoRF]F R`FlF RRM[M Rh[l[ RWMWW RZCZI RdCdI R]LhLh[ RaLaTeU R]QcS RlWl[ R`V]YY[ R[VRW RaO`W',
	    'PoSFlF RWTbT RWXbX Rb[h[ RWOWZ R[C[I RWObObX ReCeI RWLRP RWIUN RVLjLjSi[',
	    'PoRF]F RaFlF RSKkK RWSgS RWXgX RR[l[ RWNWX RYCYI RdCdI RWNgNgX',
	    'PoSFlF RSJlJ RURiR Ra[f[ RZCZH RdCdH RbJfN RYOdNlP R]IXNRP RYRWWgVf[',
	    'PoSFkF RXRfR RWVhV RS[k[ RZCZI R_R_[ RdCdI RWK`KdOlS R^KZORS',
	    'PoRElE RRKlK RZCZH RdCdH RUNZP RbVjZ RdMaPVU R_H_K[NYP\\SSV RiPfT^XS[',
	    'PoSFkF RYOeO RWZgZ RWSW[ RZCZI RdCdI RWSgSg[ R_HbKlP R^H[LRP',
	    'PoRFcF RSKcK RRRcR Rg[l[ RWCWI R^C^I R^K^[ RfGfU RlDl[ RTZR[ RWKWSVX',
	    'PoSElE R^JkJ R\\OlO Rc[g[ RXQX[ RZCZH RdCdH RgOg[ RXITN RYNTS',
	    'PoSFlF R`NlN R`VlV RZCZI ReCeI RgJg[ RVRZUT[ RVORS RXJTQ RWM^MZV RaP`U',
	    'PoRFlF RVNhN RVRhR RRVlV RVJVR RZCZH R_R_[ RdCdH RVJhJhR',
	    'PoSFlF RSK`K RRQaQ Rf[j[ RZCZI R]K][ RcLcV RdCdI RjJj[ RVKVSUX',
	    'PoRF]F R`FlF RRKlK Rf[j[ RTOT[ RYCYI R[O[Z RcOcZ ReCeI RTOjOj[ R_K]O',
	    'PoRFlF RRRlR RSKSO RZCZI RdCdI RSKkKkN R^IaK R\\N[QVVk[ RfSbW^YZZR[',
	    'PoRFlF R`NlN RUKUV RZCZH R[J[[ RdCdH R`ObTfYl[ RbJ^Q RiNfUbY][ RZTRW',
	    'PoRFlF RSKkK RUQiQ RUViV RR[l[ RZCZI R_R_[ RdCdI R]K[MWQ RcNiP',
	    'PoRGlG RYRlR RZZkZ RWMW[ RZCZI R[KcKcZ RdCdI RVNRR RXJUP RiJaK',
	    'PoSFlF RSKlK R]OiO R[TlT R_[c[ RXOX[ RcRc[ RdCdH RZLRT RZC[HZM RiPcR',
	    'PoRElE RRIlI RUQjQ RfXjX RZCZG RdCdG RTMjMjP RTTjTjX R_G_UcYk[ RTPTU R^U[YR[',
	    'PoRFlF RRKlK RWQiQ Rg[l[ RWKWP RZCZH R_S_[ R_H_K RdCdH RgSg[ RlXl[ RWSWWS[',
	    'PoRFlF RSL]L R`LkL RRR^R R`RlR RdCdH ReKe[ RXWS[ RfHeL RZCZHYLYSXX',
	    'PoRElE RcOhO RX[\\[ Rf[j[ RZCZH RdCdH RWWS[ RdW`[ RUJiJhO R_G^K[NTO RXPVX RePcX R`SkSj[ RSS]S\\[',
	    'PoSFlF RUP\\P RTU\\U Re[j[ RX[\\[ RZCZI RUK\\K\\[ RdCdI R`Z^[ R_NkNkVj[ RUKUSSZ RdKbW',
	    'PoSFlF RUKeK RSOlO RZ[j[ R[S[[ RZCZI R^H^O RdCdI RiIeM RjXj[ ReMYTRU RhSZV',
	    'PoSFlF RUMlM RZCZI RdCdI RYQ]U RgIiK RkWk[ RhPbWfZj[ R`PcWY[ R`LcV R_IbT RUMUSTY',
	    'PoRFlF R\\PlP R\\ZlZ RYKY[ RZCZI RdKdZ RdCdI RSLWP RYTRW',
	    'PoRFlF RZNlN R[[l[ RZCZH RcCcH RTJWM RbOeQkW RWXSZ RaR^UZW RbJaS RRQWQWX[Za[',
	    'PoSFkF RTKjK RRSkS RZCZI RdCdI RWMYP RgMdQ R^T[XS[ R_I_ScXgZl[',
	    'PoRFlF RaKhK R]KdK RRTlT RZCZH RdCdH RcTc[lZl[ R\\H_N RkPjS R[TXYR[ RTLXL[L_KaPeRjR RgN^PTR',
	    'PoRFlF RUM]M R`RkR RRVlV RUJUR RZCZH R_R_[ RaJaQ RdCdH RkNkR RhK`M R^PTR',
	    'PoRFlF Re[j[ RTKTU RZDZH RTK^K^U RdKdV RdDdH RkJkZ R\\X`[ RYNYVTZ',
	    'PoRFlF RUK`K RRNlN RZCZH RdSd[ RdCdH RbPkT R_KZQRT RYYS[ RZSYY RgI_J',
	    'PoRFlF RXPfP RSTkT R\\ZhZ RYZeZ RVZcZ RZCZI RdCdI RdVi[ R\\TU[ R`KlP R_H\\LRP',
	    'PoRFlF RSKkK RZCZH RdCdH ReSe[ R_H`K RYXV[ RXK[N_PgR R^PkS ReKcN_P[QRS RYSYY',
	    'PoRFlF Re[i[ RZCZH RdCdG R`RZX RdU^[ RWTVWR[ RTIXK RROWP RfOcW RaO_T R[JhJ\\O RZOkPj[',
	    'PoRFlF RWOgO RRTlT RZ[_[ RSKSN RZCZH R_H_K R_O_[ RdCdH RSKkKkN',
	    'PoSFkF RSPkP RTJTN RZCZI R_L_[ RdCdI RTJkJkN R_PfWlY R_PXWRY',
	    'PoRFlF RUNiN RVSiS RSWlW RSISL RZCZH R`P`[ RdCdG RSIkIkL R\\KZOVS',
	    'PoRFlF RZ[_[ RSKSN RZCZI R_M_[ RdCdI RSJkJkN RhPcU R_MbSfXlZ RSQ\\QZUVXRZ',
	    'PoRFlF RVPiP RRUlU RSJSN RZCZH R_L_[ RdCdH RSJlJlN RXNTR',
	    'PoRFlF RSKSO RZCZI RdCdI RSKkKkO RhQdT R_PaUdXk[ RWPUT R^TZXS[ R_N]V',
	    'PoRFlF RVNhN RUQhQ RRUlU R`[e[ RZCZI RdCdI RfSf[ RUJhJhQ RXW]Y',
	    'PoSFkF RWOiO RZCZH RdCdH RWJiJiO ReSkY RdOfU RWJVRSY R[SdU RXXeZ RVMUV',
	    'PoRFlF R`[d[ RZCZH RdCdH RdJd[ RhNlV R_N]W R\\QRS RSK\\KYNX[T[',
	    'PoRFlF R`OjO R`UjU Re[j[ RSJS[ RZCZH RdCdH R`JjJj[ RXP\\S RSJ\\JZLWO R[VWW R`J`S_X',
	    'PoRFlF RRTlT RZCZI RdCdI RSKjKiO RYM]O RUPYR RbWk[ R`T\\YS[ RaM_V',
	    'PoRGlG R^MkM R][l[ RZCZI RdCdI RdMd[ RXKSQ R[OSV R[URV R[YR[ RZPRQ',
	    'PoRFlF R_MlM Rd[h[ RZCZH RdCdH RhIh[ RWKRP R\\MSV RaQcV R\\TRU R\\XRZ RYPRP',
	    'PoRFlF Re[i[ RZCZH RdCdH RaQeT RXJRP R[MSU RbI_O R`MkLkTi[ RZORP R[TRU R\\XRZ',
	    'PoSFlF RSN]N R^PkP R\\[l[ RXJX[ RZCZI RdJd[ RdCdI RXO\\T RUTRW RXNTV',
	    'PoRFlF RUNjN R`ThT R]TeT RZTbT RWU`U RbWkW R`WiW R]WfW RZWcW RVX`X R`[k[ RZCZH RYR`Q`Z RdCdH RUJjJjN RUJURTY RgP^Q',
	    'PoRFlF RVOiO RVSiS RTWjW RR[l[ RVKVS RZCZI R_K_[ RdCdI RVKiKiS',
	    'PoRFlF RSKkK RVUhU RR[l[ RVOVU RZCZI RdCdI RVOhOhU ReVcZ RZWZ[',
	    'PoRE]E R`ElE RRM[M R^NkN R\\SlS RWIW[ RXCXG ReCeH ReUjZ RcJcSfW RcU_Y[[ R[RRT RS[V[S[',
	    'PoRElE RcShS RZ[e[ RZUZ[ RZCZH RdCdH RgUlZ ReXd[ R\\SbV RUVTZ R^J]NZQTS RVLcP RTJiJhR',
	    'PoRFkF RSVZV RSKSW RYCYH RSKZKZV RcCcH RbOdQ RgVfZ RcJfNlP R\\TiTgX RcJ_O[P',
	    'PoSFlF R[KlK R\\VdV Rd[i[ RXMX[ RZCZI R\\O\\X R\\OdOdV RdCdI RiKi[ RXLRR',
	    'PoRFlF RSJkJ RRUlU RZ[_[ RSMSQ RZCZH R_H_M R_S_[ RdCdH RSMkMkQ RXQfQ_S',
	    'PoRFlF R_KiK RRPlP RWJWO RZCZH R_H_W RdCdH RYRSW RgSdV_YW[ RaYU[',
	    'PoSFlF RRN[N Rd[i[ RWJWW RZCZI RdCdI R`PdR R_I\\O RfT^X R[URX R^MkLkTi[',
	    'PoRFkF RYPYY RYCYH RdCdH ReUkZ RbU\\[ RYJSQ RXQSW RcJcQfW RjMhR R^M\\S RW[S[ RSKXNYV RcMbU',
	    'PoSFkF RYNfN RSSkS RZ[_[ RZCZI R_N_[ RdCdI ReUjZ RXUTZ RaJlO R^H[LRP',
	    'PoRFlF RRQlQ RZCZH RdCdH RfJjO RWJ^K_O RVJTO R[NYRWViZ ReSaW]YS[ RgH]J',
	    'PoRFlF RZCZH RdCdH RVM]MaLfLhN RcNjQ R\\IUM RZOSR R[STV RZSfScV_XV[ RZS\\VbYl[ R`XS[',
	    'PoRFlF RULlL RUQ_Q RXY[Y RZCZI RdCdI RdVh[ RfJiK RbQfY RiOgSaX]Z RlWk[i[ RaIcV RULUSSZ R\\Q[Y',
	    'PoRFlF RUJkJ RXNhN RURlR RZCZI RZRZZ RdCdI R`SdXk[ RiTcW RUJURTY R_ZW[ RbYZZ',
	    'PoRF^F R`FlF RTMjM RR[l[ RZCZJ R_M_[ RdCdJ RXS]X ReSjX RfOcW RYOUW',
	    'PoRFlF RTP_P RY[^[ Rf[j[ RTJTP RZCZH RTJ_J_P RcKcV RdCdH RjJj[ RWSTZ RWPUY RWT_T^[',
	    'PoRFlF RROlO RdZiZ RXRXZ RZCZG R`Q`[ RdCdG RWTiTiZ R]MWTRV RXHiM ReIVM',
	    'PoRFlF RSJkJ RUQiQ RUTiT Rd[l[ RUMUT RZCZH RdCdH RUMiMiT R_J_U[YR[ RYW^Yh[',
	    'PoSFlF RZMlM RYZlZ RWMW[ RZCZI RcJcM RdCdI RWMRR RXJUO RhOeX R]O_X',
	    'PoRElE RSKlK RUSiS RUWiW Rd[i[ RUOU[ RZCZH R_H_[ RdCdH RUOiOi[ RfIjJ',
	    'PoSFlF RUO]O RUT]T Re[j[ RUJUY RZCZH RdCdH RbV][ R_MkMkUj[ RdKcV RUJ]J^XRY',
	    'PoRFlF RfOkO RZCZH RdCdH R^JfJfO RWWS[ R^M[P RTJYL RROWP R]SaWeYl[ R[RhRdW`YY[ R^J]O',
	    'PoRFlF RSP`P Rf[j[ RZCZH RZKZ[ RdCdH RdKdV RjIj[ RYP_V RXSRX RZNWT R_JSK',
	    'PoRFlF RTPZP R\\PlP RRTZT R]ZlZ RTJTP RZJZ[ RZCZH RdCdH RdJdZ RTTSZ',
	    'PoSF^F RaFlF R^NlN RcQkQ RcVlV RWMW[ RYCYH RcMc[ RfCfI RXLRR R_MZQ R_J]O',
	    'PoRFlF RVShS Rc[l[ RVLVS R[C[H RcScZ RdCdH RVLhLhS RXHZK RfHbL RlXk[ R[SZX RYYR[',
	    'PoRFlF RSMlM RZ[_[ RZCZI R_I_[ RdCdH RVOZS RfRcT RiOeR RcIhK R_LbSfWlZ R\\UTY',
	    'PoRFlF R_TcT RZCZI RcJcT RdCdI RhMlP RWVSZ RTJYL RROWP R]L[Q RiSeXaYZ[',
	    'PoRFlF RRM[M RZCZH RdJdT RdCdH RjNkR R`M]S RZSRT RWIW[S[ RiSeWaYZ[',
	    'PoRF^F R`FlF Rc[l[ RZCZH R[M[Q RdQdZ RdCdH R[MkMkQ RWVSZ RlXl[ RTJYL RROWP R`U]YX[ RbJaU',
	    'PoRFlF RVRhR RWUgU RSXkX RZCZH R_P_[ RcCcH R\\ITM RZLaNkP RYKfKaM]OSQ',
	    'PoRFlF RVPhP RUZjZ RUTU[ RVJVP RZCZH RdCdH RVJhJhP RUTjTj[ R^P]T',
	    'PoRElE RVKlK RRRlR RUXlX Ra[g[ RZCZH RdCdH RWJRN R]OaP R\\TaV RWMUY RWNiMhUg[',
	    'PoRFlF RbPjP RTZjZ RTJT[ RZCZH RZSZZ RbJbO RZSdSdZ RdCdH RTJjJj[ R\\J[OWQ',
	    'PoRFlF RRKlK RWZgZ RWSW[ RZCZI R_H_R RdCdI RWSgSg[ R`KcNlR R^K[NRR',
	    'PoRFlF RSJkJ RTTjT RR[l[ RZCZH R_T_[ RdCdH R]OaS RUOYS ReOiS RhLeO RXLUO R`L]O',
	    'PoRF]F RaFlF RSJkJ RVQiQ RVUiU RRXlX RVMVU RYCYH R_H_[ RfCfH RVMiMiU',
	    'PoRFlF RUKiK RRQlQ RTVjV RZCZH R_H_K R_Q_[ RdCdH RYMZP ReLcP',
	    'PoRFlF RRMlM RUQhQ RXZiZ RYTY[ RZCZH RdCdH RVJhJhQ RWUiUi[ R^J[QWURW',
	    'PoRFkF RcQkQ R^WkW R\\[l[ RZCZH R^O^V RdKdW RdCdH RXVS[ RRKYKVMTQZQVX RTUYYb[ RiJ]K',
	    'PoSFlF RSJYJ R\\PlP R]WkW R][l[ RZCZI RdJdW RdCdI RXVS[ RWLVP RUQZQVX RUUZYc[ RiJ\\K',
	    'PoSFlF R`NlN RWMW[ RZCZI R[K[X RdCdI R`NcUgYl[ RXITR RbJ]S RiMfTbY][',
	    'PoRFlF RUUjU RR[l[ RZCZI R_I_[ RdCdI RWN\\R RfNkS RfNaS RWNRS RXJUQ RgJdQ',
	    'PoRElE RWNhN RRRlR Rc[l[ RSJSN RZCZH R_H_K RcRcZ RdCdH RSJkJkN RlXl[ R[RYWXYS[',
	    'PoREkE RZLdL RVZhZ RVTV[ RZCZH RdCdH RVThTh[ R`HjM RVPgPdT R]IRN',
	    'PoSElE RRMlM R`[g[ RZCZH RTJ_I_R RdCdG R]NSS R[SZWXYS[ RaNgQlR RVScSbWhVg[ ReI_I RgHaI',
	    'PoRFlF RUQiQ R_UjU R^[l[ RUKUP RZCZI R_Q_Z RdCdI RUKiKiP RWVR[ RXSVY RXV[Yd[',
	    'PoRFlF RSKlK RZCZH RdCdH RXO[S RfPkT RWPST R_H_RaUdXj[ RfMdR RXMUS R^TZXT[ R_N`T',
	    'PoRFlF RRKlK RWRkR RY[f[ RWKWR RYUY[ RZCZH RdCdH R^TaX R_H`K RfXf[ RiUkY RTURZ',
	    'PoRF]F RaFlF RRKlK RbSjS RZWdW RTZjZ RTNT[ RYCYH RbKbS ReCeH RTNjNj[ R[QXT R\\K[R',
	    'PoRFlF RZZlZ RYPYY RZCZH RdCdH R\\JiJiS RYJTP RXQSV RW[S[ RTJXNYV R]RiQiUhZ RaJ`Q_Z',
	    'PoRFlF RWQgQ RWVgV Rc[g[ RWMW[ RZCZH R_H_M RdCdH RWMgMg[ RdJlM RYJVLRN',
	    'PoSElE RWMgM RWQgQ RWTgT Rb[l[ RWJWT RZCZH RcTc[ RdCdH RWJgJgT RlXk[ R[VYYR[',
	    'PoRFkF RVNhN RVShS RYCYH R_H_K RdCdH RVJhJiSbW R^SbXgZl[ RVJWZRZ RaY[YUZ R^YXZ',
	    'PoRFlF R^JcJ R[JbJ RWOgO RRUlU RZ[_[ RZCZH R_R_[ RdCdH RfJkO RVJTN RVJ^K_N RfP_R RgI`I',
	    'PoRFlF RROlO RT[Y[ RYJY[ RZCZH RdCdH ReWh[ RgIjM RiRcX]Z RlXk[i[ R^IRK RbIdV R`SRU',
	    'PoRElE RVMiM RVQiQ RRUlU RVIVQ RZCZH RdCdH RVIiIiQ R_UbXfYl[ R`R^V[YS[',
	    'PoRFlF RVTiT Ra[l[ RVMVS RaTaZ RdCdH RUNiNiS RlXl[ RZCZHVMRO RYJcJ_N^UZXWZR[',
	    'PoRFlF RSN]N RSR]R RfWjW RTZ^Z RSKSR RTSTZ RZCZH RSK^K^R RaKa[ RdCdH RaKjKjW RXIVK R]TSV',
	    'PoRF^F R`FlF RUNjN RUQjQ Rc[i[ RUJUQ RZCZH RdCdH RUJjJjQ RSUjUi[ R^UZYR[ R_J_Q^V',
	    'PoRF^F R`FlF R[NlN RZCZI RcJc[ RdCdI ReRlX RdOfS RWVSZ R`RZY RTJYL RbO_T RcMaR RROWP',
	    'PoRFlF RRN[N RWJWW RZCZI RdCdI RbPeVhYl[ R_MjMhQ R`J]Q RaU^YZ[ R[URX RbOaV',
	    'PoRFlF RTKjK RRRlR RZCZH R_H_[ RdCdH RWMYP RfMeP R_QdWlZ R_QZWRZ',
	    'PoRFlF RZKkK R\\PjP RZUlU R\\[l[ RZCZH RdMdY RdCdH RSIWL RUXR[ R_H\\P RRPWPWX[Za[',
	    'PoRFlF R]MlM RSQZQ RSXZX Rc[h[ RSKSZ RZCZH RSKZKZX RdCdH RhIh[ R_QbU',
	    'PoRFlF RVOhO Rf[j[ RTST[ RVJVN RZCZH RdCdH RVJhJhN RTSkSk[ R^TgX R_S\\VWX R_O^U',
	    'PoRFlF RURiR Ra[k[ RUKUT RZCZH RaRa[ RdCdH RUKiKiR ReThX RlXk[ R^H]K R]UZXT[ R_K]U',
	    'PoSGlG RYOeO RS[k[ RZCZJ RdCdJ RdWc[ RWSYX R^R`X R`JlP RgSdY R_H\\KRP',
	    'PoSFlF R[OlO RYOYY RZCZH RdCdH ReUk[ RfKjL RbU\\[ RcJcQgX RdO`X RYLRO RYQUTRV RX[T[ RTJXMYU RcLbU',
	    'PoRFlF R[NlN Rd[l[ RYOYY RZCZH RdPd[ RdCdH RfJiM R`V[[ RlWl[ RYJVM RYPSV RbP^X RTIXMSO RbJ`V RX[T[ RXLYU',
	    'PoRFlF RUPiP RVUhU RS[l[ RSKSO RZCZI R_P_[ RdCdI RdWgX RSKkKkO',
	    'PoRFlF RcRfR RRXfX Rd[j[ RSISM RZCZH RdCdH RWMgMfR RSIkIkM R^K\\M R[OaQ RWMWTkTj[',
	    'PoRFlF RfQjQ RTY\\Y Ra[k[ RTST[ RYCYH RTS\\S\\Y RaRaZ RdCdH RlWl[ RTIdIdMkMjQ R[KXOSQ RkSaV',
	    'PoRFlF R]LlL R_UjU RSU[U Re[l[ RZCZH R_O_U RdCdG ReIe[ RjOjU RlXl[ RVKRO R[MSU RWIUN RYORP R[XRZ',
	    'PoSFlF RRKlK RRTlT RZCZH RdPd[ RdCdH RYWV[ RbKeNlQ R]JYORQ RZQYX',
	    'PoRFlF RSJkJ RYOeO RRUlU RZCZH R_L_S RdCdH RdQd[ RYWV[ RcLkP R^HZMRP RZRYX',
	    'PoRFlF RSLaL R]T`T Rg[k[ RTOTT RZCZH RZIZ[ RTO`OaT RdKdV RdCdH RkJk[ RVXRZ RZSTY RZVaY',
	    'PoRFlF Ra[k[ RSKSN RZCZH R_H_K RaOa[ RdCdH RSKlKlN RlXk[ RXORT RWP^P[V RUR\\TXYS[ RaOiOiUeU',
	    'PoRElE RTIjI RULjL RROlO RVVhV Rc[h[ RVRV[ RZCZG R_H_O R_R_V RdCdG RVRhRh[',
	    'PoRFlF RSS[S RSY[Y Rd[i[ RSMS[ RZCZH RSM[M[Y RdCdH RbQeT RWIVL RbI^P RaMkLkTi[',
	    'PoRFlF R[JkJ R[UiU R[ZlZ RZCZH R[J[Z RdCdH R[OiOiU RVWS[ RWUVX RTJXK RROWP',
	    'PoRE^E R`ElE RSJlJ RVQhQ RVThT RVMVT RYCYH R_H_[ ReCeH RVMhMhT R^TjZ R\\VSZ',
	    'PoRFlF RWRgR RWZiZ RSJSN RWMW[ RZCZH R_H_J RdCdH RWMgMgR RWUiUi[ RSJkJkN',
	    'PoRFlF RUO]O RUS]S RUJUX RZCZH RSJ^J][ RdCdH ReUjZ RcY_[ RhTaZ RbNfW R`LkLfV RbLdT R]WSX',
	    'PoRFlF R^NlN RRN\\N R`ZkZ RZCZI R`S`[ RdCeIeS R`SkSk[ RTT\\X R\\PYVVYR[ RWJTU RdFeN',
	    'PoRFlF R]NkN RRNZN R[ZlZ RS[V[ RWJW[ RZCZI RdCdI RdJdN RZSRT RhOfZ R`OaY',
	    'PoRElE RXMeM RROlO RZ[_[ RZCZG R_O_[ RdCdG RVQZT RfScU RhQeT R_QdWkY RYHWM RYIfIeO R]VRY',
	    'PoRFlF RUSgS Rd[i[ RZCZH R^M^[ ReCeH RXNYQ R^RUY RdNcQ RXJRP R\\SaUdW RVLkLkTi[e[',
	    'PoRF^F R`FlF R\\MkM R[RlR RSVYV RSKSX RSKYKZV RZCZH RcIc[ RdCdH ReSkZ RcRbT]YZZ',
	    'PoRElE RXN^N RWQgQ RTZjZ RTJT[ RZCZH R`M`X RdCdH RTJjJj[ R_PdUgW R_PZUVW RdL\\M',
	    'PoRFlF RZ[d[ RYCYH RZVZ[ RdCdH RhWl[ R]MaN ReXd[ RVWRZ R_GbJlO R\\TaV RVQgQdV R_G\\KRO',
	    'PoRFlF RUMiM RUQiQ RVU^U RUIUQ RVSV[ RZCZH RdCdH RUIiIiQ RbSa[kZl[ RjTaV RYZR[ R[ZU[ R_ZXZ',
	    'PoRFlF RZKlK R\\UdU Rd[h[ RZCZI R\\O\\W R\\OdOdU RdCdI RiKi[ RUWT[ RTJXK RROWP RWTUZ',
	    'PoRIlI RTNjN RUQjQ RRUlU R\\C\\I RbCbI RVDWG RYJZM RiDfG ReJcL R_N_UbXeYl[ R_U[XR[',
	    'PoRElE RUUiU RUZiZ RUQU[ RZCZH R_Q_Z RdCdH RUQiQi[ R\\L_O RTLXO ReLhO RfJcK RWITL R_I\\L',
	    'PoSFkF RVMhM RVQhQ RRTlT RVIVQ RZCZH R_Q_[ RdCdH RVIhIhQ RaVkZ R]VR[',
	    'PoSFkF RVMhM RVQhQ RRTlT RVIVQ RZCZH R_I_[ RdCdH RVIhIhQ RaVk[ R]VR[',
	    'PoRElE ReNiN RY[\\[ RZCZH RUJ\\J\\[ R`J`[ RdCdH R`JjJiN RWNZO RWRYT RbQdUhYl[ R`QjQhVeYa[ RUJUTSZ',
	    'PoSFkF RUViV Re[i[ RUQU[ RZCZI R\\Q\\[ RbQbZ RdCdI RUQiQi[ R_IiN R_H]JSO RYNdMlN',
	    'PoRElE RVLhL RVPhP RTVjV RTZjZ RTST[ RVIVP RYCYG RdCdG RVIhIhP RTSjSj[',
	    'PoRF^F R`FlF RRN[N R^SkS R^ZkZ RS[W[ RWIW[ RZCZI R^M^[ RdCdH ReJeZ R^MkMk[ R[RRT',
	    'PoRFlF RRM\\M RbZkZ R]ZfZ RXIX[ RZCZH RdCdH RXP\\T RgUkY RbJgJjP RXLSV R`K]Q RcP`Z',
	    'PoRFlF RSNjN RRSlS RSWkW RU[i[ RXNXW RZCZH RVK`K_[ RdCdH RfNfW ReJ_J RiIaJ',
	    'PoRFlF R^KjK R\\KgK RYKdK RVLbL RaTkT R_TiT R\\TgT RZTdT RXUbU RZCZH RdCdH RkOkQ RlXk[ R`IdI R`QeR RSL^K`NcPhQ RRUYU^T`XdZi[ RfW_YU[ RfM^OTQ',
	    'PoRFlF RTM]M RaMjM RTP]P RaPjP Re[j[ RTIT[ RZCZG RTI]I]P RaIaP RdCdG RaIjIj[',
	    'PoSElE RTJ]J RRSlS RZCZH R_Q_[ RdCdH RiKgP RVMWP R^L^O R`ScVlZ R^SZWRZ R_J[J RhI_J',
	    'PoRFlF RRMZM R\\MlM RWIW[ RZCZH RdCdH RfJiK RgT`[ R`MdUgYl[ RhPeW R_R\\Z R[RRT RS[V[S[ RaI_V',
	    'PoSFlF RRO]O Re[i[ RXKX[ RZCZH RdCdH RWO\\U RcU^Z RVTRX ReObX RXOUV RbJ^R RfMcV R\\JRK RaMkMkSj[',
	    'PoRElE RVTiT RVLVS R_M_S RdCdH RUNiMiS RbTa[lZl[ ReVhW RZCZHUM RYJcJ_N R[TZXXZR[',
	    'PoRFlF R\\QjQ RZCZH RcIcP RdCdH RTIXK RROVP R^P_TcXj[ R\\LkLjO RVUS[ RiRdX\\[ R\\K\\SZY',
	    'PoRFlF RTZjZ RTMT[ RZCZH RdCdH RjMj[ RdSgV RbSeT RXM[P RfMcP R]SVV RWJfJ_M_W\\W',
	    'PoRElE RRMZM R^TfT RfWiW R^[k[ RS[W[ RWHW[ RZCZH R_N_Z RdCdH R^NfNfT R^KjKjU RlWkZ R_K[O R`H]M RZRRS',
	    'PoRFlF RSK]K RST^T RSY^Y RSKSY RYKYO RYTYY RYCYI RSO^O^T RdCdI RdTj[ RgS`[ RaMeV R_KjKfV RaKcS',
	    'PoRFlF RUMiM RRQlQ R^WdW RY[g[ RYTY[ RZCZH RdCdH RYTdTdW RWI[K RfIbK RhXg[ RcQlU R_H]MWRRU',
	    'PoSElE RRM^M R`PlP RXIX[ RZCZH RdCdH RgPg[ RXN]S RXMTV RjI`K`S_Y',
	    'PoRFlF R]PkP R[[l[ RZCZH RdCdH RfPfY RSIWL RRPWPWXRZ RVY[Za[ R]K]Q\\V RiI]K',
	    'PoSFlF RUJlJ RZSkS RZNZ[ RZCZH ReCeH RdVi[ RbWc[ R_HaJ RbQfY RZNbNdW R_YYZ RlXl[i[ RUJUSSZ RhMaM',
	    'PoRFlF RTJkJ RROlO RRSlS RZCZH R_G_J RdCdH RYKZN R\\QVW RdKcN R^OXT RXVjZ RfSbW_Y\\ZS[',
	    'PoRFlF RTKkK RRQlQ RVZhZ RVTV[ RZCZH R_H_K RdCdH RVThTh[ RYLZO ReLdO',
	    'PoSElE RSOlO RXZiZ RTKTN RYSY[ RZCZH R_H_K ReCeH RXTiTi[ RTJkKkN R[PTW',
	    'PoRFlF RSIkI RUQiQ RTTkT RUWiW RR[l[ RUMUQ RZCZH R_I_[ RdCdH RUMiMiQ',
	    'PoSFlF RdQiQ R^ZjZ RZCZH R^S^[ RdCdH R^SjSj[ RaM]Q RTIXK RROWP RVUS[ RbJ_P R]JjJiP',
	    'PoRFlF R[QhQ R\\ZiZ RZCZH R\\S\\[ RdCdH R\\SjSj[ RfLkQ RYTSZ RbJ[Q RTJXL RROVP',
	    'PoRFlF RUJkJ RWQlQ RYTiT RZCZH RdCdH RYMiMiT R`GaJ R`K`TdXj[ R`T]XV[ RUJUSTZ',
	    'PoRFlF RTJjJ RRNlN RSSkS RRWlW RXJXS RZCZH R_J_[ RdCdH RfJfS',
	    'PoREkE RaZhZ R_ZeZ RT[W[ RWNW[ RZCZH RdJdY RdCdH RSJ\\JWO RfVi[ R\\QRS RiIiOjY R^K^R]Y RjI^K',
	    'PoRFlF RVJiJ RRMlM RcQlQ RZCZH R_H_M RcMcQ RdCdH R[STW R[MXQRR RZTfScV`XWZ RaXS[ RZT\\W`Yl[',
	    'PoRElE RSL[L RbLkL RbQkQ RbUlU RZCZH RbIb[ RdCdH R[VXYS[ R\\I[URV RTQ[P[W',
	    'PoSFkF RXPfP RUTiT RS[k[ RZCZI R_P_[ RdCdI RXVZY R_IaLlP ReVdZ R_H\\LRP',
	    'PoSFlF RRJlJ RWSgS RWWgW R_[k[ RXNXY RZCZH R`L`[ RdCdH RVPhOgW RkXk[ RbJfOlQ R]HWORQ',
	    'PoRFlF RVOhO RWSgS RZXfX Rf[k[ RTJT[ RZCZH RZSZX RdCdH RTJkJk[ RZL\\N RdLaO',
	    'PoRFlF RTJjJ RUMiM RRPlP RYTeT RUWjW RZCZH R_R_[ RdCdH RbMfRlT R_H\\NWSRU',
	    'PoRElE RRKlK RUOhO RdZhZ RSRSU RVUVZ RZCZG R_R_[ RdCdG RVUhUhZ RUHhHhO RSRkRkU',
	    'PoREkE RRL^L RXIXL RZCZH RdCdH RbPfT RfJlP RdK^P R[[W[ RkW`Z RVLTY RVQ\\Q\\V[[',
	    'PoRFlF R]PiP R]UiU RX[l[ RZCZH R]J][ RdCdH R]JiJi[ RVXS[ RXTWW RTJYK RROWQ',
	    'PoRFlF RVMiM RYTjT RYWjW Re[j[ RYPY[ RZCZG RdCdG RVIiIiM RYPjPj[ R^GaI RVIVRUX',
	    'PoRFlF RRN^N R_NlN RYIY[ RZCZH RdCdH ReIe[ RYP]T RhTlW RaS]X RUTRW ReLiT RXMTV ReLaU',
	    'PoRElE RZCZH RdCdH RhIeM RhRdV RYJUM RXSTW RbMjQ R^MiO R_QaVdYl[ R_I]M[OSQ R_Q]VZXWZR[',
	    'PoRFlF RXL^L RRP_P RV[Y[ RYIY[ RZCZH RdCdH ReUk[ R]S^W RbMfW RUSSX RaJdT R`JjJiPfU`Z^[',
	    'PoRFlF RUJiJ RROlO RZ[][ RZCZH R^R^[ RdCdH RgUiY RfThW RbUcY RcPlT RXTVY R^LYQRT',
	    'PoRFlF RSK`K RSQ_Q RTU`U Re[j[ RZCZI RZRZY RdKdV RdCdI RjJj[ R]MaQ RYKSP R\\YSZ R_YVZ',
	    'PoRElE R^JiJ R^PeP RXQ_Q R^SiS Rc[k[ RZCZH R_N_R R^H^L R\\UcUc[ RdCdG RkXk[ RULkLjO R[UZYV[ RbP\\P RULUTTY',
	    'PoRElE RSKkK RXNfN RXRfR RRVlV RXHXV RZCZG RdCdG RfHfV RdWl[ R[WR[',
	    'PoRFlF RRO_O RaXjX RYKY[ RZCZH RaKa[ RdCdH RaKjKjZ RXO^U RWSRX RYNVU R]JSK',
	    'PoRFlF RTKjK RRUlU RZCZI R_H_K R_R_[ RdCdI ReM`R RYNTS RdOiQ RYO]Q',
	    'PoSFlF RXOdO RUSfS RWYdY Re[i[ RWUWZ RZCZH R]N]Y RdUdZ ReCeH RVKRO RWHUN RYLWQ RVKkKkUi[',
	    'PoRFlF RSIjI RUNhN RVWhW RVZhZ RUKUN RVSV[ RZCZG RdCdG RhKhN RVShSh[ R_G_QkPkQ',
	    'PoRFlF RUNhN RURhR RRWlW RUJUR RZCZH RcSc[ RdCdH RUJhJhR R_H\\J R^RWU R_J^R',
	    'PoRElE RWIiI RWMhM RWPhP RRSlS RWIWZ RYCYH RdCdH RiUcX R^SbWfYl[ R]ZU[ R_YXZ RaY[Z',
	    'PoSFlF RWMhM RWQhQ RWUhU Rb[k[ RWJWT RZCZH RcUcZ RdCdH RWJhJhT ReVhX RlWk[ R\\VYYR[',
	    'PoRFlF RUN\\N R_RkR RUS\\S R`[k[ RX[\\[ RZCZH RUJ\\J\\[ R`J`Z RdCdH ReJeR R_JkJkR RkXk[ RUJUUTZ',
	    'PoRFlF RSKkK RZCZH R_H_[ RdCdH RXO[R RfOjS RWPST R_PeWlZ RfMdS RXMUR R_PXWRZ',
	    'PoRElE RSIkI RROlO RURhR RRUlU RR[Y[ RZCZG R_G_R RdCdG RULhLhR R]RWXk[ RaYT[',
	    'PoRFlF R`OkO RSP[P R`UkU RSU[U Rf[j[ RSKSX RZCZI RSK[K\\U RdCdI R`KkKk[ R_W[[ R`K`R_W',
	    'PoRElE R[JkJ RYTlT RZCZH RcJc[ RdCdH RgMkQ RVWSZ R^MZQ RVUUY RTJXK RRNWP',
	    'PoRFlF RSLlL RSTlT RZCZH RTJ_I`Q R\\RWW R_LeOlQ R_LXPRQ RXWkZ RaI\\I ReCeI_I RfTcW_YT[',
	    'PoRElE R`OeO RUU]U RaUiU RUZiZ RUQU[ RdCdH R`QiQi[ RZKgJeO RZCZHVNRO R]PUQ',
	    'PoRFkF RTJjJ RRPlP RUShS R_ViV R][l[ RZCZG R_H_Z RdCdG RUMhMhS RXVS[ RYYb[',
	    'PoSFlF RWMkM RWQiQ RWUiU RWYlY RWKW[ RZCZH RaKaY RdCdH RWKRQ RXHVN RaHaM',
	    'PoRElE RRJlJ RZSgS Rc[k[ RZCZH RVSdScZ RdCdH RkXk[ RcOhT R^H`J RcLjO R[LSO R]N[PVR RZXS[ R[SZX',
	    'PoRFlF RWSgS RWWgW RR[l[ RSKSO RWOW[ RZCZH R_H_K RdCdH RWOgOg[ RSKkKkO',
	    'PoRFlF RRM[M RdQiQ R^ZjZ RS[V[ RWHW[ RZCZH R^S^[ RdCdH R^SjSj[ RaM]Q RbJ_P RZRRS R]JjJiP',
	    'PoRElE RRJlJ RWWgW RZCZH R_H_J RdCdH RXSgRg[ RWXT[ RYK^NbOlQ ReK`N[ORQ RWRWY',
	    'PoRFkF RVN]N RaNhN RVRhR RbZlZ RVJVR RZCZH RcRcZ RdCdH RaJhJhR RlWlZ R[SZWXZR[ R]IUJ',
	    'PoRFlF RWNgN RSSkS RZ[_[ RSKSN RZCZH R_H_K R_S_[ RdCdH RSKkKkN RhWkZ RXUUYRZ ReViW',
	    'PoSFkF RRKkK RUSiS R[[_[ RZCZH R_S_[ RdCdH ReViZ RbKfO RXVTZ RYOdOlQ R]IXORQ',
	    'PoRFlF RRN]N Re[l[ RXIX[ RZCZI R_M_Q RdCdI ReJeZ R_MlMlQ RlXl[ RXO]T RcU^Z RXMTV ReJdU',
	    'PoRFlF RRM[M R]SjS R\\WkW RT[W[ RWIWZ RZCZH RdQd[ RdCdH R_JaMeOlQ R[RRS R]JiJgMbPUR',
	    'PoRFlF RVLiL RVOiO RVSiS RRVlV RVIVS RZCZG RdCdH ReTe[ RVIiIiS RZTYXS[',
	    'PoRFlF RdMlM RYX\\X R_YkY RSMSX RWIW[ RZCZH RSM\\M\\X R_Q_[ RdCdH RdIdQ R_QkQkZ',
	    'PoRFlF RTNkN RTJTN RZCZH R[J[N RcJcN RdCdH RTJkJkN R[QTT RYRhReV`XXZ RZUaXS[',
	    'PoRFlF RUU^U Ra[k[ RUJUP RURU[ RYCYH RaRa[ RdCdH RUJjJjP RlXl[ R^LfP R_L\\OXP RjT`V R^ZT[',
	    'PoRFlF RRO]O RYKY[ RZCZH RdCdH RlXl[ RcIcSeV R_RcS RWO\\U RaW^[ RVSRW RXNUU R^MiMk[i[ R\\JRK RcMbW',
	    'PoRFlF RY[][ Rf[j[ RZCZH RUJ]J][ RdCdH RaJjJj[ RWNZO RXRZU RdMhO RcSgT RUJUSTZ RaJaT`Z',
	    'PoRFlF R]SjS R]ZjZ RZCZI R]M][ RdDdH R]MjMj[ RcJaM RYUS[ RULYM RSPWR',
	    'PoRFlF R\\TeT R][k[ RZCZH R]T][ RdCdH R\\NeNeS R]KjKjU RTIXK RVWS[ RlXk[ R]LYO RROWP RWUWW R^H[N RiVfV',
	    'PoRFlF RWOgO R_TiT R_[l[ RSKSN RZCZH R_O_Z RdCdH RSKjKjN R_H`K RVWR[ RXRTY RWU\\Ye[',
	    'PoRElE RUTiT RTZfZ RSJSM RUOUT RZCZH R_L_Z RdCdH RUOiOiT RSJkJkM RfVl[ RjYfY',
	    'PoRFlF RWRgR RVZiZ RSJSN RVUV[ RZCZH RdCdH RWNgNgR RVUiUi[ RSJkJkN RWNXR',
	    'PoRFlF RVP`P R]TiT RZTfT RTTbT R[[_[ RSJSN RZCZH R_T_[ RdCdH RSJkJkN RePjU RdWkZ R]LVO RYWSZ RdM[RTT',
	    'PoRFlF RRMlM RTPiP RZCZH R_H_[ RdCdG RiSi[ RUJiJiP RcTeY R[TYY RUSUY',
	    'PoRFlF R]KkK R\\PlP RTJT[ RZCZH RdCdH R_MaO RcIdK RgMfP RXPZT RTJZJWO RYVWV R\\Q[Y',
	    'PoSFlF RRO]O RXKX[ RZCZH RdCdH RfVk[ RWQ[U RcU^[ RUTRX RdJdRgX RdQaX RjMhR RXOTV R`M_R RXLWS R\\JRK RdMeU',
	    'PoRElE RRO^O RYJY[ RZCZH RdCdH RhIh[ RZR]U RaPdS RbJeM RVTRW RXNUU R\\IRJ RkU_W',
	    'PoRFlF RRMlM RWWgW RWZgZ RWSW[ RZCZG RUJ`I_R RdCdG RWSgSg[ R_MbNlS R\\NRS RgH`I',
	    'PoRElE RULiL RUPiP RWXfX Rf[k[ RSSS[ RUIUP RYCYG R_I_W RdCdG RUIiIiP RSSkSk[ RdUgY',
	    'PoRElE RVPiP Re[j[ RTST[ RVLVP RZCZG RdCdG RVLiLiP RTSjSj[ RXX_XeVfY RTJ_I`W]X RdI_I RiHcI',
	    'PoRFlF RZMdM RUT^T RUW^W R[[^[ Rf[i[ RUPU[ RZCZI RUP^P^[ RcQcX RdCdI RiPi[ R_IfLlN R_IXLRN',
	    'PoRElE R_LdL RVQhQ RVThT RVWhW RVNVW RdCdG RVNhNhW RYJeJdL RZCZGVLRN RcYl[ R[YR[',
	    'PoRFlF RSJlJ RUSiS RUMUS RZCZH R_H_[ RdCdH RUMiMiS RZO[R RdObR R_ScWl[ R_S[VR[',
	    'PoRFlF RVNhN RWTgT RWXgX RR[l[ RSJSN RWQWX RZCZH R_H_K RdCdH RWQgQgX RSJkJkN',
	    'PoRFlF RTJaJ RTNjN RRQlQ RYThT RZCZH RdCdH RVKXM R^K`M RYTTZ RhJdN RZPWW RYT`Xi[ R[NYV RgVaXY[ R_Xl[ RiHaI',
	    'PoRFlF RRKlK RXSfS RWVgV Rf[j[ RTNT[ RZCZH R_H_N R_S_[ RdCdH RTNjNj[ R[P]S RdPaS',
	    'PoRFlF RTN^N RTR^R RTV^V Rf[j[ RTJTV RZCZH RTJ^J^V RdCdH ReJeV RjIjZ R\\W_[ RVWS[',
	    'PoRElE R^LgL RYYeY Rf[j[ RTQT[ RWIWQ RYTYY RZCZH R^L^Q RdCdH RYTeTeX RWIgIgQ RTQjQj[',
	    'PoSFkF RTJjJ RUMiM RSPlP RXWfW RXZfZ RXSX[ RZCZH RdCdH RXTgSf[ RdRlU R^H\\NWRRU',
	    'PoRFlF RSR`R RSU`U Re[j[ R][`[ RSNS[ RZIZN RZCZH RSN`N`[ RdCdH RdKdV RjIjZ RTJVM R^J]N',
	    'PoRElE RTO\\O RbOjO RTT\\T RbTjT RTKTT RZCZH RdCdH RbKjKjT R_H_RaVdYl[ R\\JTK R^T[XXYR[ R_N^V',
	    'PoRElE RVJbJ RVPiP RWWjW Rg[j[ RVIWTW[ RZCZH R]S][ RdSd[ RWSjSj[ RVLiLiP RUNTY ReCfHaI',
	    'PoRElE RWLgL RWNgN RVThT RVWhW RVZhZ RTITO RVQV[ RZCZG RdCdG RVQhQh[ RTIjIjO',
	    'PoRFlF RTJjJ RRPlP RWVhV RWZhZ RWSW[ RZCZH RdCdH RWShSh[ RYLZN R^HaJ RdLbO',
	    'PoRElE RUM]M RaMiM RUPiP RRSlS Ra[f[ RUIUM RZCZH RUI]I]M RaIaM RdCdH RaIiIiM RYSWWhVf[',
	    'PoRFkF R]ZiZ RZCZH R]T][ RdCdH R]TiTi[ RROWP RaHZN RVWSZ R^KbOlT RTIYK R_KiKeO[T',
	    'PoRFlF ReSiS RR[l[ RUUU[ RZCZH R\\U\\[ RbUb[ RdCdH RUUiUi[ RXOST R[OcS RYIVR RXMbM_QYT RTIeIdMjMjS',
	    'PoRFlF RZOlO R\\YiY RZCZH R\\S\\[ RcKcS RdCdH R\\SiSi[ RVXS[ RTJYK RRNWP RXTVX RiJ]K',
	    'PoRFlF RRMZM R\\OlO R^YjY RS[W[ RWIW[ RZCZH R^S^[ RdJdS RdCdH R^SjSj[ R[RRT RiJ]K',
	    'PoRFlF RSJkJ RTMjM RRQlQ RRTlT R[C[G R_J_P RcCcG R^QWWk[ ReV`X\\ZS[',
	    'PoRFlF RTS_S ReWiW R\\[k[ RUMUS RZCZG R\\S\\Z RSO_N_S RdCdG RYVTZ RcT`W RlWkZ RVJ\\J[N RWJRO RWHTN RaMkMkRiW RYOYW ReIdT',
	    'PoRFlF R_MlM RcQkQ RSQZQ RcVkV RSXZX RSJSZ RYCYH RSJZJZX RcMc[ RdCdH R`I]P',
	    'PoSFlF R`MlM RUO\\O RdQkQ RUT\\T RdVkV RZCZH RUJ\\J\\[ RdLd[ RdCdH RaI^P RX[\\[X[ RUJUTSZ',
	    'PoRFlF R_PiP R_UiU R\\[l[ RZCZH R_J_[ RdCdH R_JiJi[ RWL[P RWT\\Y RWLSQ RXIUO RXQTZ',
	    'PoSElE R\\OiO RYSlS RWLW[ RZCZH R\\J\\O RbOb[ RdCdH R\\JiJiO R]XYZ RbR[Y RVMRR RbRhXlZ RXIVO',
	    'PoRElE RSMjM RSTlT RZCZG R\\R_R_[ R_M_R RdCdG RiNeO RaVk[ R]VR[ R[JdM RVIgI^K R_MXPSQ',
	    'PoSFkF RWRhR RRUlU RWNWR RZCZH R_R_[ RdCdH RWNhNhR R\\HYK RSLfKjM RaVjZ R^USZ',
	    'PoRElE RRKlK R^NfN RWQkQ RRUlU RWHWQ RZCZH R^H^N R_Q_[ RdCdH RfHfN R]VSZ RaViZlZ',
	    'PoRElE RWRgR RRVlV RWNWR RZCZG RdTd[ RdCdG RWNgNgR R_GiL RZVXZT[ R^HTL RZLcKlL RZTYX',
	    'PoRFlF RUO\\O RTT\\T R`VgV Rf[i[ RZCZH RUJ\\J\\[ R`O`X RdCdH R`OgOgW R`LkLkY RaH^O RX[\\[X[ RUJUTSZ',
	    'PoRElE RSJkJ RRS\\S R^SlS RZCZH RdCdH R\\UXYS[ RiTfX`[ RdPaWl[ RWPTW]Z R\\GXMjP ReJbM_OTQ',
	    'PoRFlF RRJlJ RWPhP RTWhW RTZjZ RTST[ RWLWP RYCYH R_S_Z RdCdH RWLhLhP RTSjSj[',
	    'PoSElE RZNlN R[QiQ R[TkT RZXlX RWPW[ RZCZH RbHb[ RdCdH R[JjJjQ RXHSM RYMRT',
	    'PoRElE RYNlN RZQiQ RZTjT RXXlX RZCZH RbIb[ RdCdH RZJiJiQ RTIXK RROVP RVTS[',
	    'PoRFlF RaSeS RR[l[ RUUU[ RZCZH R\\U\\[ RbUb[ RdCdH RUUiUi[ RbIkN R[JSN RXMfMeS R]NZQTS',
	    'PoRElE RSL^L R`MlM RRP_P RSU^U Rd[h[ RXIXP RXRXY RZCZH RdCdH RiIi[ RbQdV R_YTZ',
	    'PoRElE R^KkK RRR]R R]ZlZ RXRX[ RZCZH RdCdH RdKdZ RZO]R RVJTLWOUR R[T\\X RTTSZ RZKXO',
	    'PoRFlF R[NkN RZTlT RZCZI R_J_T RdCdI RgJgT RhXk[ RfWiX RXUR[ R]YY[ R_W[Z RTJYM RRPWR',
	    'PoSFlF RYMlM R\\QjQ RYUlU RWLW[ RZCZH RdCdH R\\JgJeM RcVfYj[ RWITQ R\\N[S RbV_YZ[ RbQaW',
	    'PoRElE RcJkJ RcOlO Rd[h[ RWPW[ RZCZH RdCdH RhOh[ RaU[Z RWITM RXNST R]JaL R[Q`R',
	    'PoRFlF RbOiO RRTlT RSJSM RZCZH R_H_J RbJbO RdCdH RSJkJkM RbQgR R`UdYl[ R[JYOSQ R_Q]VZYR[',
	    'PoRFlF RUJeJ RROlO RYViV RYZiZ RYRY[ RZCZH R]H]O RdCdH RWSiSi[ RiIfL\\QRT',
	    'PoRElE RRLlL RUUiU RUZiZ RUQU[ RZCZH R_Q_Z RdCdH RUQiQi[ R\\L`P RTLXP RdLhP RhHeK RXHTL R_H\\L',
	    'PoSFlF RRN]N R`PkP R`UkU R`ZkZ RXJX[ RZCZI R`J`[ RdCdI R`JkJk[ RXO]T RUTRW RXMTV',
	    'PoRElE RSJkJ RYMeM RYPeP RRSlS RVZjZ RVSV[ RYGYS RZCZG RdCeGeS R]S\\VXW RbSbWiViW',
	    'PoSElE RWLhL RWOhO RWYeY Rd[i[ RWIWO RWSW[ RZCZG RdCdG RWIhIhO R^UcX R_T[X RVTRV RZOTU RWSjSi[',
	    'PoSFkF RSJ]J RTM]M ReQiQ RSUkU RXHXR RZCZH RdCdH RcN_Q R_TcYk[ RdIbO R_U[YS[ R_IjJiQ R[PTQ',
	    'PoRElE RSJ_J RRT_T Re[j[ RYTYZ R[C[H RdCdH R\\O_R RROTS RTLRO R^L\\O RYLXOYR RdS`[ RaMkMkTj[ ReIeNcX R_YTZ',
	    'PoRFlF R\\JlJ R_OiO RSPZP R_SiS ReWlW R`XiX R\\XdX RT[X[ RZCZH RRJZJZO R_J_X RdCdH RiJi[ RRORUYUX[',
	    'PoRFlF RVNiN RVQiQ RTTjT RUXiX RR[l[ RVJVQ RZCZH R_T_[ RdCdH RVJiJiQ R_H]J',
	    'PoSFlF RZLlL R]NiN RWLW[ R[C[H RdCdH R]IiIiN R[Q[RkQkT RWMRQ RXIVO R_UaXeYl[ R]ThTeXaY[[',
	    'PoSElE RVNfN RVTeT RVWeW Ra[e[ RVQV[ RZCZH R]L][ ReCeH RVQeQe[ RWJRO Ri[f[ RVKkKkUj[',
	    'PoRFlF RRKlK RR[l[ RVTV[ RZCZI R\\T\\[ RbTb[ RdCdI RVThTh[ R^OaR RbKgQlS R]H\\MWRRS',
	    'PoRFlF R_IgI RTJbJ RRLlL RURiR RUUiU RTXjX RR[l[ RUOUU RZCZH R_I_[ RdCdH RUOiOiU',
	    'PoRFlF R`MlM RRS^S Rd[h[ RXSX[ RZCZH RdCdH RhIh[ R[P^S RTMWP R\\U^Y RYNUS RbQdV RWIUM RTURY',
	    'PoRElE RRKlK RUOU[ RbObW RZCYH[K RdCdHbK RWQ[S RWU[W RUO]O][Y[ RiNi[e[',
	    'PoRElE RRLlL RVRhR RUUjU RRYlY RUUUY RZCZG R`R`[ RdCdG RVIgIgM RVOhOhS R]G\\M RVOVS',
	    'PoRFlF RRK`K RRVYV Re[j[ RU[Y[ RYIYN RYTY[ RZCZH RSNRP_N_Q RdCdH RcW_[ RTQ]RYS RaMkMkVj[ ReIeNcW R\\UVV R_UXV',
	    'PoRFkF RUMiM RUPiP RRSlS RUJUP RVSV[ RZCZG R_J_P RdCdG RUJiJiP RhUbX R]SaWdYk[ R^YUZ',
	    'PoRFlF RSR[R R^RjR RSY[Y R_[k[ RSLS[ RZCZI RSL[L[Y R_K_[ RdCdI RdKdR R^KjKjT RlWk[ RVIVM',
	    'PoRFlF RUQ`Q RTV`V Rf[k[ R\\[`[ RZCZH R[M[U RTN`N`[ RdJdV RdCdH RkIk[ RWJRN RVK]JZM RUMUTTZ',
	    'PoRElE RRM^M RbOjO RaTjT RSY]Y Rf[j[ RSRS[ RXIXR RZCZH RSR]R]X RdCdH RbJjJk[ R^Z][ RbJbRaX',
	    'PoRElE RSIkI RaRkR RRVlV RZCZH RbIbQ RdCdH RdSd[ RYIVM[P RXLRN RWM^L[PUS RYYS[ RjLaN RYSYX',
	    'PoRElE RTO]O R`OjO R_RkR RTS]S RTW]W RTJT[ RZCZH RTJ]J]N RdCdH R`JjJjO RaTeXl[ RjSfX_[',
	    'PoRElE RUN\\N R^RjR RUR\\R RUJU[ RZCZH RdCdH R`JhJhOlNlP R`SdXk[ RiSeX\\[ R`J_O R\\IUJ R]VTW',
	    'PoSElE RRR^R Re[i[ RXRX[ RZCZH RdCdH R[O_S RTKWO RbQeT RXIUL R[KTR R\\T^X RTURZ RbH_P RaMkLkUj[',
	    'PoSFlF RZMjM RdQkQ RWMW[ R[C[H RdCdH RfJiKkM R^UYW RaQ[V RWMRR R`I]L RXIVO R`N]PZR RdLdQ R^TaWeYl[ R^ThTdW`YY[',
	    'PoRElE RdSgS RZ[e[ R[U[[ RdCdH RgVl[ RbN]R ReYd[ RZCZHSN R]KVR R]TaV RUVTZ RcJ`P RXMcO RWJiJhS',
	    'PoRFlF RVMhM RZ[_[ RZCZH R_Q_[ RdCdH RVJhJhP R^G^J R_RcWkZ RST[TXXRZ RVJVPhQfUcV',
	    'PoRElE RUJlJ RWNaN RUQaQ RZCZH RfHiI RfT`[ RgZj[ RXU_Y RiMeV RlWk[ R[NWV R^R\\WZYVZ RdCcHeV RUJUTSZ',
	    'PoRFlF RULlL RWO_O RXW_W RXRXY RZCZI RXR_R_W RdCdI ReWi[ RfIjJ RcQfY RiOgSaY][ RbMeX RlXk[i[ RaHcU RULUTSZ',
	    'PoRFkF RTJ]J RUUiU RZCZH RdCdH RfIdK RiMeN R_UcYk[ R_P]VZYS[ RZPcP RaJgPlR R\\KXORR RULXO',
	    'PoRFlF RRJlJ RWOhO RVUhU RZ[_[ RSRSU RWLWP RZCZH R_G_J R_U_[ RdCdH RWLgLgP RSRkRkU',
	    'PoRElE RULiL RVRiR RVUiU RRXlX RSISL RVOVU RZCZG R_J_[ RdCdG RVOiOiU RSIkIkL',
	    'PoRFlF RUNiN RURiR RY[e[ RUJUR RZUZ[ RZCZH R_J_R RdCdH RUJiJiR RgUlZ ReXe[ R\\TaV RUUSZ',
	    'PoSFlF R`LlL RRL]L RhVjV Ra[l[ RZCZH RVP\\P\\Y RaNa[ RdCdH ReMeW RkPkV RlXl[ RaI^O R[[W[ RkP]R RXIWLTY',
	    'PoSElE RVLhL RROlO RWRgR RWUgU RZYlY RWYjY RRYgY RVIVL RWOWY RZCZG RdCdG RhOh[ RVIhIhL',
	    'PoRElE RYUeU R]YfY RZYbY RWZ`Z RYPYU RZCZH RXM`M_Y RdCdH RYPePeU RcWgZ Rj[l[ RlWl[ RUIhIhTi[ RUIUSTY RdK]L',
	    'PoRElE RUNhN RbRkR RYUfU RUIUN RZCZG RdCdG RhIhN RbNbR R\\RZTSW RZIdL RcH^KXL R\\NYRSS RZV_YbZl[ ReV_YR[',
	    'PoRFlF RRIlI R[PcP RRTlT RTMTQ RZCZH R[I[P RcIcP RdCdH RTMjMjQ R\\QWWkZ RfTcW_YS[',
	    'PoRFlF ReOiO RZ[e[ RZUZ[ RZCZH RdCdH RaJiJiO R^TaW RaJaSkRkS ReXe[ RXIRN RiVkZ RWNZO RUUS[ RVK^K\\NYQRT',
	    'PoRFlF RVMjM RZSiS RZWiW RZZiZ RZPZ[ RZCZG R`I`M RdCdG RZPiPi[ RVIjIjM RVIURTY',
	    'PoRFlF RWMgM RWQgQ RR[l[ RUTU[ RWJWQ RZCZH R\\T\\[ RbTb[ RdCdH RWJgJgQ RUTiTi[',
	    'PoZFlF RZNlN R\\[l[ R_C_I RbJbX RfCfI RUDWG RSJVM RdPkW RfKiL RRPWPWWR[ R`QZW RdNeR RaN_R RYZa[',
	    'PoRElE RSJkJ RdZhZ RSOSR RVSVZ RZCZH R_H_J R_O_[ RdCdH RVShShY RSOkOkR RYLZN ReKdN',
	    'PoRFlF RWMeM RROkO R\\[`[ RZCZG RdCdG RdThXlZ R_RaWa[ RYHWM RYJeJdO RaOTS R_STV RhQ\\WSZ',
	    'PoSElE RUM]M R`OlO RRQ_Q R[ZkZ RSISL RZCZH R\\Q\\Z RdCdH RSIkIkL RbQcT RlXkZ RTZR[ RhLhXdX RVQUY',
	    'PoSFlF RgQkQ RSTlT RZCZH R_Q_[ RdCdH R[JgJgP RkNkQ RYMTR RUHXI RSKVL R`TcVlZ R`M^OZQ R^T[WRZ RaH_N',
	    'PoRFlF RSJ\\J RhQkQ RRTlT RXJXO RZCZH RdCdH R_JhJhP RkNkQ RaMeO R_J^P R]RWWk[ R\\ORP RfTcX_YT[',
	    'PoSFlF RUKlK RYNYV RZCZH RYNaNaV RdCdG R^WaY R]P\\WXZ RgUb[ RgHiI RjNfX RlWk[i[ RdKfX RUKURTY',
	    'PoRElE RUK]K RaOkO RVVhV RVZhZ RUHUP RVSV[ RZCZH RaHaO RdCdH RVShSi[ RkMkO R^P\\R RjI`L R^NTP',
	    'PoRFlF RVMhM RROlO RVIVM RVRVW RZCZG R_H_O RdCdH RVIhIhM RVRhRhW RaXk[ R\\YS[',
	    'PoRFlF R`QkQ RTY\\Y RTST[ RYKYQ RZCZH RTS\\S\\Y RaIaP RdCdH RkNkQ RbSa[kZk[ R[IXLSN RiJ\\N RjTaV',
	    'PoRElE R[JaJ RZRlR Rd[h[ RXHX[ RZCZH RdCdH RhPh[ R\\L^O R^UaX RSKUO RiKgO RXSRU RbKbO RiH_J',
	    'PoRFlF RSMlM RRTlT RZCZH R_H_R RdCdH RXI[L RfIcL R_MePkQ R^MXPRQ R\\RWWk[ RfTcW_YS[',
	    'PoRFlF Rf[i[ RZCZH R\\W\\[ RdCdH RXHYJ RdVfX R\\MSW RaWaZ R_H\\N RXWV[ RZQhPhT RTLfLeP RUUkTj[',
	    'PoRFlF RYCYI R`O`[ RdCdH RgVl[ RXTR[ RdPhX RTJYL RRPWQ RlRfT RgY^Z RiM`O R\\L\\TZZ RiJ[L',
	    'PoRElE RTLjL RbTiT RTWiW RTZiZ RSHSK RTOT[ RZCZG RbLbS RdCdG RTOjOj[ RSHkHkK R[QXT R\\L[R',
	    'PoRElE RUNiN RURiR RbXiX RUJUR RZCZH R_J_R RdCdH RUJiJiR RbRa[lZk[ RhViY ReTcX R[RYWWYR[',
	    'PoRFlF RTO]O RhOlO R_RjR RTR]R RX[\\[ RZCZH RTM]M]R RdCdH RbJhJiO RaSeXl[ RiTeX^[ RTJTV]U\\[ RbJaO RTQSZ R]ITJ',
	    'PoRElE R^JlJ RSP[P RhXkX RSY\\Y RSKS[ RZCZH RSK[K[P RST\\T\\Y R_N_X RdCdH ReJe[ R_NkNkX RWHVK',
	    'PoRElE RRR\\R R_UjU Re[l[ RWRW[ RZCZG R_N_W RdCdY RjNjV RlXl[ RZO\\R RYJXN RVHSLWNTR R[T\\X RTTRZ RjJ]L',
	    'PoRFlF R]LkL R[PlP RSQZQ R\\SlS RSXZX Rc[h[ RSJSZ RYCYH RSJZJZX RdCdH RdIdO RhPh[ R^UaX',
	    'PoRFlF RWQhQ RR[l[ RUTU[ RWIWQ RZCZH R\\T\\[ RbTb[ RdCdH RWIhIhQ RUTiTi[ R_K[O R^MdO',
	    'PoRElE RTIjI RXOgO RXRgR RXTgT RRWlW RXLXW RZCZG RdCdG RXLgLgW R^G^L R[YS[ RcYkZ',
	    'PoREkE R\\OjO RZCZH R\\K\\Z RcIbK RcCcH R\\KjKjS RTIYJ RRMWO RbScVfYl[ RWSS[ R\\SjTdW RdYYZ',
	    'PoRElE RbNjN RTTjT RRWlW RZ[_[ RZCZG RVQ_Q_[ RbIbN RdCdG RbIjIjN RXHUM^N RSJ^J[MXNSP RgP_Q',
	    'PoRElE RWLgL RROkO RSISL RZCZG RdCdG RSIjIjL R`OaSeWlZ RjQcT R`[\\[ RaOSS R]TSV R\\Q_T`WYYR[',
	    'PoRFlF RSJlJ RVPhP RVWhW Re[j[ RSSS[ RVLWP RZCZH RdCdH RVLhLhP RSSkSk[ RZT\\V RcTaV R_W_[',
	    'PoSFkF RTM\\M RSQ]Q R^RlR RTSTY RXLXX RZCZH RdCdH R_LiLiR ReUjZ RcV][ RdJdRgX RUISO RdNcV R\\S\\YUY',
	    'PoSFkF RTK]K RaKjK RRP^P R_PlP Rb[e[ RZCZH RdCdH RfPf[ R\\S]W RiSlX RTSSY RbS_Y RXPX[U[',
	    'PoRFlF RUJkJ RYPiP RYTiT R^[a[ RYMYT RZCZH RaTa[ RdCdH RYMiMiT RhWkZ RfViX R\\VXZ RaJ`M RUJUSSZ',
	    'PoRFlF R_LlL R^[l[ RZCZH R\\L\\[ RdCdH ReIeL RTIXK RRNWP RVUS[ R]IZQ RiNgX RaObX',
	    'PoRElE RRL]L R`VfV Re[i[ RZCZH R`O`W RdCdH R`OfOfV RaH^P RWHXL RZNYV RTNUV R`LkLkUj[ R\\WRY',
	    'PoRFlF RVMhM RVQhQ RVThT RY[f[ RVJVT RYWY[ RZCZH RdCdH RVJhJhT R^UaX RhWlZ RgXf[ RUWTZ',
	    'PoRFlF RSJkJ RURiR R_[f[ RRNRR RZCZH R_H_J R_P_R RdCdH RRNkNlR RZLZN ReKcN R\\RZWXYS[ R[VgVf[',
	    'PoRElE R^OiO R[SkS R_[c[ RSJS[ RZCZH RcOc[ RdCdH RWOZS RhVlZ R_V[Z RcIfLlO RSJZJWO RcI_M[O RYVVV',
	    'PoRElE R^OiO R\\SkS R_[c[ RWPW[ RZCZG RcOc[ RdCdG RhVlZ R_V[Z RXISN RYMRT RcIfLlO RcI_M[O',
	    'PoRFlF R\\OgO RZSjS R^[a[ RZCZH RbOb[ RdCdH RfVjY RUJXK R]VYY RbIdLlO RROVP RVUTY RbI^MYO',
	    'PoRFlF RePiP RUViV RUZiZ RTJTP RURU[ RZCZH R_R_Z RdCdH RURiRi[ RcL_P RdJbO R_JjJiO RZL\\OSO R[ITJ',
	    'PoRElE RVJiJ RVMhM RVOhO RVRkR RWJWR RZCZG R_I_R RdCdG RYHSM RZWaYk[ RSUgUbX^YS[',
	    'PoSFlF RUJlJ R\\ObO RXXaX RXSXY RZCZH R\\L\\R RXSaSaX RdCdH RgHjI RfUa[ RiNeX RlXk[i[ RcJeW RUJUSSZ',
	    'PoSFlF R[JkJ R\\MjM RZOlO R\\VjV Re[j[ RWLW[ RZCZH R\\R\\[ RcIcO RcRcV RdCdH R\\RjRj[ RWMRR RXIVN',
	    'PoRFlF RRO]O R_OiO R_UiU R\\ZlZ RXJX[ RZCZH R_J_Z RdCdH R_JiJiZ RXP\\U RVTRW RXNUU R\\IRK',
	    'PoRElE RSIlI RVOhO RVRhR RRUlU R_[d[ RVLVS RZCZG R_G_S RdCdG ReTe[ RVLhLhS RXW]Y',
	    'PoRFlF RTKkK RUOjO RSSlS RXWiW RU[l[ RZCZG RaVa[ RdCdG RZI[K ReIcK R_K\\RXWRY',
	    'PoRFlF RVThT Ra[l[ RSJSM RWOWT RZCZH RbTb[ RdCdH RUPhPhT RSJlJlM ReVhW RlXl[ R[KWORQ RYMcM`P]VZYR[',
	    'PoRElE RWNdN RePiP RWXdX Rd[i[ RWTWX RWKWN RZCZG R^I]N R^S]X RdCdG RdKdN RdTdX RXHSM RXPRV R\\OTQ R\\YS[ RVJjIjP RVSkSj[',
	    'PoSFlF RWPjP RWSiS RWViV RWZjZ RSJSM RXOX[ RZCZI RaNaZ RdCdI RSJkJkM R_LbO R_HYNRQ',
	    'PoSFcF Rg[l[ RWCWI R_C_I RfFfU RlDl[ R^SaT RUSXT R^ObO RUOXO RTKYKY[V[ R]KcKc[`[ RTKTUR[ R]K]U\\[',
	    'PoRElE RhOlO R`RjR RRS]S RZ[][ RZCZH RUK]K][ RdCdH RbJhJiO RXNZP RXU[W RXIYL RbSfXl[ RiSeX_[ RbJaO RUKUUTY',
	    'PoRElE R[KlK RRMZM R\\RkR R\\VkV Rg[k[ RWIW[ RZCZH R\\O\\[ RcIc[ RdCdG R\\OkOk[ RgHjJ RZSRT RS[V[S[',
	    'PoRElE RZKlK R[RkR R[VkV Rg[k[ RZCZH R[O[[ RcHc[ RdCdH R[OkOk[ RTIXK RRNWP RgHjI RWTTZ',
	    'PoRFlF RSNYN RSS\\S Rh[k[ RZCZH R^M^[ RdCdH R^MkMk[ RdPjU RcR`V RXISN R[KSS R[U\\Y R[P\\T RXVXZ RSVS[ ReJdR',
	    'PoRElE RRM_M RbOkO RbTkT RTT^T Rf[j[ RTOTV RZCZH R^O^V RdCdH RbJkJk[ RUIVL RXVT[ R]I\\L RYMYRXW RbJbSaX',
	    'PoSFlF RVPbP RgRiT RVShS RSVlV RZCZG RdCdG RWKXM RTJ_J`L RgJeL RdQhR R^MVO RfNUS R_UcYl[ R_V[YR[ RcI^I RiHbI',
	    'PoRFlF RSS[S Rd[h[ RWVXZ RZCZH RdCdH RgKlP RXISN RYNSS R[LUR RaK]P R[Q\\T RTVS[ RaI_N R_Z^Z R[V[Z RcQaY R_QiQhZ RZNSN',
	    'PoRElE RWLhL RURiR RUUiU RUWiW RUOUW RWIWL RZCZG RdCdG RWIgIgL RUOiOiW R[YR[ RcYl[',
	    'PoRElE RRM[M R[S_S RWUgU RZCZH R\\X\\[ R_M_S RdCdH RhWk[ RaWb[ RVWR[ RbLcOgSlT RWJfJ_M RZNVSRU RjLdO',
	    'PoRElE RRKlK RRQlQ RUThT RZCZG R\\H\\[ RcIc[ RdCdG RUNhNhT RbSfWlZ R\\TXXRZ R_HUI RhH_I',
	    'PoRElE RWJlJ R\\NjN RYSkS RZCZG RaGaJ RdCdG RSKUO RbRdVfYl[ R]KZQ RWRRT RbNbU^XX[ RWJWRVX',
	    'PoRFlF RTN]N RaOjO RSR]R RX[\\[ Re[i[ RTNTS RZCZH RSJ]J]N RdCdH R`JjJjN RWSRV RZUWXRZ RdT_U RgUeW`Y RaN`RkRi[ R]S\\[',
	    'PoRElE RVPhP RVShS RXZiZ RVMWVX[ RZCZH RdCdH RVMhMhS RXViVi[ R_HjM R^ISM R[KcKlM RWPUY',
	    'PoRElE RYLeL RVSiS RZ[e[ RVIVS RZUZ[ RZCZH RdCdH RVIiIiS RgVlZ RUWR[ ReYd[ R_NeQ R\\TaV R_K]OYQ',
	    'PoRFlF RZPkP R[[l[ RZCZH RbJbX RdCdH R]K^N RUIWL RSMVP RRSWSVXS[ RbO[V RiKfN RbOhUlV RYZa[',
	    'PoRElE RRJlJ RXPgP RYYeY Rf[j[ RTST[ RXLXP RYUYZ RYCYH R_H_J RdCdH RYUeUeY RXLgLgP RTSjSj[',
	    'PoRElE R[TkT RS[W[ RWNW[ RZCZH RdTd[ RdCdH RhVlZ RgPlU R]KcK_N RgL_T R_V[Z RRJ[JWO R]NaP R\\PRR RiI`J',
	    'PoSFkF RTJjJ RUMiM RRPlP RVViV RZCZH RXS_S_[ RdCdH RaMfQlT R^VhZ R^H\\NXRRT R`VU[ RdR\\S',
	    'PoRElE RWOgO RWSgS RRVlV RWLWS RZCZG R_S_V RdCdG RWLgLgS RdXk[ RZXS[ RSISM RSIkIkM',
	    'PoRFlF RUM\\M RbMiM RUQiQ RTThT RUKUQ RZCZH R_H_S RdCdH RbJiJiQ R[JUJ RXU^XbYj[ RgUaX]YT[ RaYR[',
	    'PoRElE RRJlJ RRQjQ RUWiW RUZiZ RUSU[ RZCZH R_S_Z RdCdH RUSiSi[ ReNlQ RVL]O R_JYL RdLYQ',
	    'PoRFlF RZKkK R]PiP R[PdP Rg[l[ RZCZH RbRb[ RdCdH RhRh[ RlXl[ RaIcK RfMjQ RVWS[ R\\XX[ R`K[P RSIXK RROWP R\\S\\Y',
	    'PoSElE RUJlJ RUMkM R[QfQ ReZiZ RXTXZ RZCZH R[J[Q R_H_J RaQa[ RdCdH RfJfQ RXTiTiZ RUJTSSZ',
	    'PoRElE R^KgK RWTgT RWWgW Rb[g[ RSNSQ RWIWN RWQW[ RZCZG R^K^N RdCdG RWQgQg[ RWIgIgN RSNkNkQ',
	    'PoRFlF RTO^O RTR^R RTKTZ RYIYK RZCZG RTK^K^R RbJb[ RdCdH RYU^Z RgPkT RbJkJiMgP RjXfX R_T\\XRZ',
	    'PoRElE RWZgZ RSJSM RXSX[ RZCZH R_G_J RdCdH RVThSg[ RSJkJkM RcLiO R_NaPlU RYMUO R^N[QRU',
	    'PoRElE R\\IdI RWOfO RX[\\[ Re[i[ RZCZG RSR\\R\\[ RdCdG R_RiRj[ R]KYO ReJlM RbLfN RZRRU RYWRZ RgR_U RfV_Z RYJRM',
	    'PoRFlF RUJiJ RRNlN RSSjS RR[l[ RVUV[ RZCZH R\\U\\[ R_H_N RbUb[ RdCdH RVUiUi[ R\\NWR RcOlS',
	    'PoRElE RVNhN RRQlQ RZCZG R_H_Q RdCdG RfHaM RXITN RXJ\\L ReJkM R[TSW R]QYU^XfZ RYUfU`XYZ R]Xj[ RbXT[',
	    'PoRFlF RUIeI RRLlL R[PaP R[RjR RVWiW RVZiZ RVTV[ RZCZG RZO[R R]G]L RdCdG RVTiTi[ RiHbLSQ RhN_O',
	    'PoRElE RUIlI RWPlP RYShS RYZiZ RYVY[ RZCZG R`G`J R`T RdCdG RYLhLhS RYViVi[ RUIURTY',
	    'PoRFkF RYKYY RZCZH RdCdH RdKdY R\\K]T R[U\\YUZ RgUhY`Z RiIiNjY R^IUJUSSZ RjI`J`T_[',
	    'PoRElE RUIkI RYKhK RUNlN RVVlV R`[e[ RZNZR RZCZG RdCdG ReSe[ R`OfR RYW]Y RiPcQ RWS]SbRlS RUIUQSY',
	    'PoRFlF RRJlJ RRPlP RWShS RWMWS RYVYZ RZCZH R_H_J RdCdH RWMhMhS RjTcW R_ScXgZl[ R]TRW R_ZV[',
	    'PoRFlF RaKkK RRK^K RSQ^Q R`QlQ RST^T RRW_W RSNST RXIX[ RZCZH RSN^N^T RdCdH RfKf[',
	    'PoSElE R[KkK RYQlQ R\\ZiZ RWLW[ RZCZH R\\T\\[ RcIcK RdCdH R\\TiTi[ RgMfP RWLRQ R^M_P RXIVN',
	    'PoRFlF RSLkL RUPiP RRUlU R[W[[ R_L_T RdCdH RZCZH[J RhWl[ RUWR[ ReIbL RbWc[',
	    'PoSFlF R^LjL RRN[N R\\QlQ R]VjV R\\ZlZ RXJX[ RZCZI RdJdP RdRdZ RdCdI RXP[S RUTRW RXNTV',
	    'PoRElE RUQiQ RVV^V Ra[k[ RUIUQ RVSV[ RZCZH RaSa[ RdCdH RUIiIiQ RlXl[ R^G^I RYKfO RdK^NXO RjUaW R[ZR[ R^ZV[',
	    'PoRElE R\\KjK RZPlP RcTjT RbZlZ RWPW[ RZCZG RcIcP RcQcZ RdCdG RXISN RYMRT R]SZZ R]V`YgZ',
	    'PoRFlF RRJlJ RWReR RYUX[ RZCZH RdCdH RePgR R^H`J RcLjN RbOfQ R^RcWkZ R[LTO R\\OWR RjTcW R`RYURW RaYX[',
	    'PoSElE RUJlJ RXUiU RV[l[ RZCZH R_H_J RaLaZ RdCdH RZO^R RfOjS RfMdR R[MXS RUJUSSZ',
	    'PoSFlF R_KkK RWLW[ RZLZX RZCZH RdCdH RVMRQ RaI\\N RXHUO RdR^U R_KbOfQlR RiKeOaQ[R RfU^W RiX^[',
	    'PoRFlF RSK_K RbMlM R\\S^S RTNTT RYIY[ RZCZH RTN^N^S RdCdH ReUjZ R[U^X RYS\\V RXTRY RiOeW RYQWV RdI`Q RbNfUbY^[ RiMgU',
	    'PoRElE RRJ^J RaPkP RSW^W RSZ^Z Rb[k[ RSMS[ RVJVR RZCZH RSM^M^[ RbPb[ RdCdH RaJkJkR RlWk[ R[JZR^R',
	    'PoRFlF RaLkL RR[l[ RUKUR RUUU[ RZCZH RZJZS R\\U\\[ RbUb[ RdCdH RUUiUi[ ReNgR RbI_Q',
	    'PoRFlF R^JeJ RUU]U RaUiU RUZiZ RVQV[ RZCZH RdCdH R`QiQi[ RVLWO RhKfO RSKZK_K_N R]PUQ RiIaJ',
	    'PoRElE RTR`R RTV`V RXZ`Z RSZ\\Z Rf[j[ RULUV RZMZV RZCZH RSN`N`V RdCdH ReJeV RjIj[ RWJRN RVK]KZN',
	    'PoRElE RSJkJ RVQhQ R]XeX RZXbX Re[j[ RTST[ RVKVP RZCZH RdCdH RhKhP RTSjSj[ RbUfY R_H_J R^SYX R^Q]U RZLeO RcK^NYO',
	    'PoRFlF R\\LjL R[OjO RZCZH RdCdH R[IjIjN RZQZRkQkT RVWSZ RSIXK RROWP R]T`WdYk[ R\\ThTeWaYZ[',
	    'PoRFlF RTO]O RbOkO RTS]S RaTkT Rg[k[ RTKT[ RZCZH RTK]K]S RdCdH RbJkJk[ RXIZK R[V]YS[ RbJbT`Y',
	    'PoRElE R_JlJ RS[W[ RWPW[ RZCZH R`N`W RdCdH R`NjNjW R[KWM RSJ\\JYM RTLZO RfXlZ RRP]P[T ReJcN RcY][ ReQdX',
	    'PoRFlF R\\KjK RZNlN RZCZH RcHcN RdCdH RRKWM RVURY RePkR R_PYS R_TYW R`R^U`WdYl[ R^UhTeWaYZ[',
	    'PoRFlF RXQfQ RTUjU RR[l[ RSJSN RZCZH R_Q_[ RdCdH RSJkJkN RYWZ[ ReWc[ R^MaNlQ R_LZORQ',
	    'PoRElE RVJhJ RVMhM RROlO RTXdX Rc[h[ RVHVM RZCZF RdCdF RXRdRdU RVHhHhM RbOlS R\\MXQRS RXSXViVh[',
	    'PoSEkE R`KkK R\\SlS RWLW[ RZCZH RZKZY RdCdH RdQd[ RdSjY RdS^Y RVLRQ RaH\\M R`KcNjQ RXHUN RiJeNaP\\Q',
	    'PoSFkF R^TiT R^WiW Re[h[ RWLW[ RZLZX RZCZH R^Q^[ RdCdH R^QiQi[ RVMRQ RaH\\M RXHUO R`LfOlP R_KiJgMcO\\P',
	    'PoRFlF Re[i[ RU[X[ RYNYZ RZCZH RdCdH RcV_[ RSJ\\JXN RbJ^N RaKdOkR RdRaY RVLXOTW RRP]P\\S R^TjTi[ RaKjKgObQ^R',
	    'PoSElE R\\QjQ R\\UjU R\\[l[ RZCZH R\\M\\Y RcMcY RdCdH R\\MjMjX RTIXJ RSMWN R\\IiIcL RRRWRWXRZ R_KgM RgYjYgY RYZa[',
	    'PoRFlF RZJkJ R[[l[ RYCYH RdCdH RTIWL RgSjV RdRhS RdJZO RbOZS RROWOWXRZ R`MdQbTZW RdTcX`X RgNcPdV RYZ`[',
	    'PoRElE R^JhJ R\\QiQ R\\TiT RZWkW R\\[l[ RZCZH RcOcY RdCdH RTHWK RRRWRWWS[ R`H[L RRMVN R]KbMjO RfKaNYP RWX[Za[',
	    'PoSFlF RTJlJ RWNlN RZThT RZWhW Ra[k[ RZCZH R[Q[Y R`H`J RaPa[ RdCdH RYRhQhW RkYk[ ReNhQlS R_L[QVR RTJTTSZ',
	    'PoRElE RZJkJ R[PjP R[TjT RYWlW R\\[l[ RZCZH R[M[S RbHbY RdCdH R[MjMjS RSHWK RRPWPWWR[ RYZa[',
	    'PoRFlF RcTjT RbZlZ RWPW[ RZCZG RcPcZ RdCdG R_KaN RgKkO RXISN RYMRT R^K[O R_H]N R]TZZ RgHeN R]V`YgZ',
	    'PoRElE RcKjK RZPlP RcTjT RbZlZ RWPW[ RZCZH R]J]P RdIdZ RdCdH RXISN RYMRT R]TZ[ R]V`YgZ',
	    'PoRElE RZLaL RcLlL RbRlR RZCZH RdCdH RgKg[ RTIXK RWSS[ RhHfL R[ZYZ RROWP RaQYS R^H]L]S\\X',
	    'PoRFlF RTIkI R[PeP RXW^W RbWiW RTZlZ RTIT[ RXSXW RZCZH R[L[P RXS^S^W RbSbW RdCdH R[LeLeP RbSiSiW',
	    'PoRFlF RSJkJ RXMfM RUSiS RTUjU RUXiX RR[l[ RXHXM RZCZH R_M_[ RdCdG RfHfM RUOiOiS RUOUS',
	    'PoRElE RSIkI RUNiN RUQiQ RRVlV R`[d[ RUKUQ RZCZG R_G_Q RdCdG ReTe[ RUKiKiQ R\\QYS RSTfSjT RWX[Y',
	    'PoRFlF R[JkJ RRLZL R[PlP R\\SiS RcVjV Ra[l[ RZCZG RcHcZ RdCdG R\\MiMiS R]UZZ RYQRT RWHW[S[ R_Zg[',
	    'PoRFlF R^MlM R^QkQ R^UkU RSVZV R^YlY RSKSY RZCZH RSKZKZV R_K_[ RdCdH RfKfY ReIfM R_I\\P',
	    'PoRFlF RRL[L R^MkM R^QjQ R^UjU R^ZlZ RWHW[ RZCZH R_K_[ RdCdH ReKeZ R`I\\R RfIeM RZPRT RS[W[S[ R_I^Q',
	    'PoRElE RRIlI RTOjO RTRjR RVWhW RVZhZ RTLTR RVTV[ RZCZG[R RcHcR RdCdG RVThTh[ RTLjLjR',
	    'PoRElE R[KcK RYOdO R_RcR RhWkW RVNV[ RZCZG R[Q[Y R_J_X RdCdG RfIf[ RfIkIkW RWHRM RVNRR RWLUP R\\HZM RcXXY',
	    'PoRElE RTK]K RRN^N RZR^R RSU^U RXY^Y Ri[l[ RZCZH RdCdH R_MiMi[ RlWl[ RdIdSfV R`QdS RVNURRS R`Z^[ RXIYNZR RdLbX RXSXYRZ R\\YVZ',
	    'PoRElE RSK\\K RRO^O RRS^S RSW]W Ri[l[ RXIXO RXSX[ RZCZH RdCdH R_MiMi[ RlWl[ RcIcReV R_QcS RTOVS R[OZS R_Z^[ RcLbX',
	    'PoRElE RZCZG RTH\\H\\O RdCdG R`HiHiO RZMSP ReI`L R^PST RgLaO RYISL R_OfSlT R_SWU RcUWX RdYU[',
	    'PoRElE RULiL RUOiO RRRlR RTUjU RRXlX RUIUO RXPXU RZCZG R_I_[ RdCdG RfPfU RUIiIiO',
	    'PoRFlF R^JkJ RTZjZ RTMT[ RZCZH R^H^M RdCdH RTMjMj[ R^N`P R_VaX RYR[T ReSgT RYP`SbUeV ReO`SXW',
	    'PoRElE R[NjN R\\VjV R\\ZjZ RWPW[ RZCZH R\\Q\\[ R_H_J RcNaQ ReCeH R\\QjQj[ RSJSKkJkM RYMWQRU',
	    'PoRFlF RSK`K RRQaQ RTY_Y RTTT[ RULWO RYIYK RZCZG RTT_T_[ RcJc[ RdCdH RgQkU RcJkJiNgP R]L\\P RkXfY',
	    'PoRFlF RTJjJ RRMlM RVRiR RVUiU RRXlX RVOVU RZCZH R_U_[ RdCdH RVOiOiU RYJ[M R_HaJ ReJcM',
	    'PoRElE RULiL RUOiO RSVjV RUIUO RZCZG R_U_[ RdCdG RUIiIiO RWQ]T RdSkV RdWkZ R_I_OYQ RdQYU RZXS[',
	    'PoRFlF R[NjN RSOYO R]UjU R`ZkZ R\\ZhZ RT[X[ RRJYJYO RZCZH R]Q]U RcMcZ RdCdH R]QjQjU RgWl[ RgJkN RaI_M RSORTYSX[',
	    'PoRElE RTL\\L RaLjL RTO\\O RaPjP RZWdW Rf[j[ RTIT[ RZCZG RZRZX RTI\\I\\O RaIaO RZRdRdW RdCdG RaIjIj[',
	    'PoRElE RTL\\L RaLjL RTO\\O RaOjO RWRgR Rf[j[ RTIT[ RZCZG RTI\\I\\N RaIaN RdCdG RaIjIj[ RdR`WWY R]QZUgX',
	    'PoRFlF R\\JjJ RZNlN ReRkR RZCZH RcHcN RdCdH ReNeR RTIXK RRNWP RVUTZ R^QYS R_TYW R`R^U`WdYl[ R^UhTeWaYY[',
	    'PoRFlF RaJkJ RSN]N R_QlQ RSR]R Rf[l[ RSKSR RZCZH RSK]K]R RTTTZ]Y]Z RaJaQ RdCdH RgOg[ RlXl[ RdU_[ RXIVK RgOcX RhJdV R\\USV',
	    'PoRElE RSJlJ RTPjP RVRhR RSUlU RZ[_[ RTMTP RZCZH R[J[P R_U_[ RcJcP RdCdH RTMjMjP RdXjZ RXXSZ',
	    'PoRFlF RRJkJ RWSgS RXNXR RYUX[ RZCZH RdCdH RUOgOgR R^H`J R_ScWkZ R[LTO RjTdW RcLjN R^TRW RaYWZ',
	    'PoRFlF R^MjM R^QjQ R]TkT R[WlW RZCZH R^J^Q RdTd[ RdCdH R^JjJjQ RWIWTZW RSMSR RZLWOUX',
	    'PoRFlF R]LaL RYPbP R[[^[ RZCZH R^I^Z RdCdH RhWl[ RgUb[ RePiY RTIXK RROVP RjOeX RVTS[ RZSXX RaSbW RdJgV RcJjKgV',
	    'PoRFkF RSK_K RTT^T RTOTS RYIY[ RZCZH RTO^O^S RdCdH RgVl[ RdV_[ RYTSZ ReRhY RYU^X ReRbX RbMkMjQ RbJ_Q ReNfV',
	    'PoSFlF RUJlJ RTSkS RYV`V Rc[l[ RYSY[ RZCZH R[J[R RcSc[ RcJcR RdCdH RUNkNkR RlXk[ R_HaJ RjUbW RUJTSSZ R_ZX[',
	    'PoRFlF RTJ[J RRM\\M RTO[O R_RiR RTR[R RSZ[Z RSUS[ RZCZH RSU[U[Z RdCdH RaJgJhOkNkO R`SdXk[ RiSeX^[ R`J`O',
	    'PoRFlF RSJkJ RZXdX Re[j[ RTNT[ RZCZG RZTZZ RdCdG RZTdTdX RTNjNj[ RYK[M R_HaJ RdKcN RaPgS R\\QWS',
	    'PoRElE RTJjJ RWQfQ RZXdX Rf[j[ RTNT[ RZTZY RZCZG R_H_J R_N_T RdCdG RZTeTeX RTNjNj[ RZK[M RdJcM',
	    'PoRElE RUNiN RURkR RUV^V RUIUN RZCZG R[I[N RbIbN RdCdG RUIiIiN R_PbVeYi[ RgSdW[[ R_NaV RdOhP RkXk[h[ RURTY',
	    'PoRFlF RRLlL RVOhO RZCZG R\\X\\[ RdCdG RVIhIhO RWRiRiV RYQYViVl[ RbWd[ RWWS[ R^I[NXRRT',
	    'PoRElE RVKhK RVMhM RTSjS RTPTS RVHVM RZCZG R[P[S RcPcS RdCdG RVHhHhM RTPjPjS RXW_Yh[ RSUhUcX[Yk[ ReX]ZR[',
	    'PoRElE RVLeL RWQcQ RVVdV RVYeZi[ RVSVZ RWNWQ RZCZG R]S]Y RWNcNcQ RdCdG RVSdSdY RWGUKRN RVJjJjUi[',
	    'PoRElE RRJlJ R[NcN RgNlN RdZhZ RSQST RVTVZ RZC[H[N R_Q_[ RcHcN RdCdG RgHgN RVThThZ RSQkQkT RWMRO RWHWM',
	    'PoRElE R_HiH R^NgN R\\NdN RXNaN R^PiP RZUhU RZXhX RV[l[ RZRZ[ RZCZG R_G_O RdCdG RZRhRh[ RUKkKkM RUKUSSZ',
	    'PoSElE RUJlJ RUOkO R\\TeT RZCZH R\\J\\T R^W_[ R_H_J RdCdH ReJeT RhVl[ RcWdZ RYVWZ RUJUSSZ',
	    'PoRElE RTKhK RdPkP RZCZG RdCdG RiNkP R\\GYJ R^M[P R`NjS RbHiK RVLUP RfLdP RSP\\PRT R`RWT RdTXW RdYV[',
	    'PoRFlF R`LlL RRL]L RdTjT RdZlZ RZCZH RdCdH ReOeZ RWIXK R`VcZ R_OjOiR RaH^O R[[W[ R`R^Z RVLUX RVQ\\Q\\V[[',
	    'PoRFlF RaNlN RTN_N RWQ_Q RVT`T Rd[h[ RX[[[ RZCZH R[T[[ RdCdH RiIi[ R^V_Y RcQeV RWVVZ RTJ_J_N RTJTSS[',
	    'PoRElE RTQ_Q RTV_V R[[_[ RZMZU RZCZH RSN_N_[ RdCdH RiIi[ RbPeS RcJfM RVJ\\JYN RWJSMTTSZ RlU`W',
	    'PoRFkF RSK^K RbMkM RRN`N RRR_R RX[\\[ RZCZH RdCdH ReUk[ RgTa[ RbNfW RjNfW RdI`Q RTZR[ RYIYNXRUY RWU]U\\[',
	    'PoRFlF RSPZP R\\PkP RSTZT RSYZY RSJS[ RYCYH RSJZJZY RcJc[ RdCdH R]K_N RcPlY RiKfN RbS[Y RcOaU',
	    'PoRElE RRKlK RVNhN RUSiS RRUlU RS[Z[ RUPUS RVIVN RZCZG R_G_S RdCdG RVIhIhN RUPiPhS R[SVX RWXi[ RfV`YU[',
	    'PoRFlF R`LlL RRL]L RaPkP R^TlT RW[[[ RZCZH RdCdH RfVj[ ReSgY RaI^O RbM_S RdVaY^[ RXIWLUY RVQ\\Q\\U[[ RePdW',
	    'PoSFlF RSS[S RZCZG RdCdG RWISN R[KSS RbI]N RYP[T RZV[Y R`LdOlS RcThV RaKjKfO^S RWVWZ RSVS[ R`XjZ RYMRN',
	    'PoRElE RUTiT R[[_[ RZCZH R_T_[ RdCdH RSMWO R`JgP RcJjJeO RXHSM RZQdPlR RVK[M ReWjY RWJ^KXQRS RXXSZ',
	    'PoRElE RRUlU RR[Z[ RZCZG RcHcL RdCdG RWOTR R\\JjJjL RTHXI RRKVL R_NcQjR R\\MhMeObQ]R R\\J[Q R[SWXi[ RfV`YU[',
	    'PoRElE RTOYO RRSYS R\\SlS Rc[g[ RTJTO RYIY[ RZCZG RdCdG RhPh[ R_UbX R^LbP RbJ\\M RaKjJgM_Q RbKeO\\Q RUSSZ',
	    'PoRElE RUZiZ RSJSM RUUU[ RZCZH R_G_J R_T_Z RdCdH RZMZSeReS RiUi[ RSJkJkM RgMkQ RVNSQ R\\K`M ReM]QST',
	    'PoRElE R]MdM RVSiS R[[e[ RVJVS RZCZH R[V[[ RdCdH RVJiJiS R]TaW RgVlZ RVWSZ ReYe[ R_G]I R^LXNcPeQ RcN^PYQ',
	    'PoRElE RVMhM RVOhO RVRlR Re[i[ RZCZH RZWZ[ RdCdH RVJhJhO RcVfY R^G^J R^W`Z RUWTZ RVJVTkUi[',
	    'PoRElE RSJ]J R`JlJ RTM\\M R`MkM R_PlP RVP]P RSP[P RRWlW RVZhZ RXHXQ RZCZG RdCeHeQ RVShSh[',
	    'PoRFlF RXMfM RWPgP R_UbU RTZjZ RTIT[ RZCZG RYSbRbU RdCdG R[Q[WeVdW RTIjIj[ RZK\\M RdKbM RdQhS R_MZRVS',
	    'PoRElE RVMhM RVTiT RVWiW RSJSM RVPVW RZCZH R_M_W RdCdH RVPiPiW RSJkJkM R^G`J RdYlZ R[YR[',
	    'PoRFlF RSK_K RbPlP RSQ^Q RST^T RRW_W RSNST RYHY[ RZCZG RSN^N^T RdCdH RhPh[ RjIbJbTaY',
	    'PoRElE RSIkI R_LhL RROlO RWSjS Rf[j[ RZCZH RZXZ[ R_I_O RdCdH ReWgY R_W`Z RTWSZ RVVkVj[ RXKXQVV',
	    'PoREkE R]KkK R]PkP RWR\\R Rh[l[ RTOTY RWMWX RZCZH RcScZ RdCdG RiRi[ RgMkQ R_X[[ RSJ\\JWN RbK`P RlXl[ R\\WRY R_S^Y',
	    'PoRElE R_LfL R\\RjR R[UlU RZZjZ RSIS[ RZCZH RdCdH RgXk[ RVPYS RcIlM R\\OgOeR RSJYIVP R`U^Z RaJYM RYTYWUX',
	    'PoRFlF RVMhM RXT]T RTZkZ RTIT[ RXPXT RZCZH RXP]P]T RdCdH RhVhX RTIkIk[ RcKfK RcWgX R`KbU RfQbV]WVW',
	    'PoRElE R[JkJ R\\QjQ R\\TjT RSIS[ RZCZG R\\M\\T RcIc[ RdCdG R\\MjMjT RcShXlZ RSIYIVO RcS^XZZ RXPYTYVVV',
	    'PoRElE RUMjM RZRhR RXXiX RY[k[ RYTYZ RZCZH RZOZR RaTaX RdCdH RZOgOgR RXTiTiX RUJjJjM R`GaI RUJURTY',
	    'PoRFlF RhPlP R_RjR RSS]S RU[X[ RXPX[ RZCZH RdCdH RaJhJiP R[U^X RTJ\\N RaSeXl[ RTURY R\\IXMRP RiSeX^[ RaJ`O',
	    'PoRFlF RUM]M RSQ^Q R[[k[ RSJSM RZCZH R[Q[Z R_G_J RdCdH RSJkJkM RlXk[ RaOcSfVjX RbL_S RTZR[ RaOiOfScV^X RVQUY',
	    'PoRFlF R[JkJ R\\MjM RZOlO R\\VjV Rf[i[ RZCZH R\\R\\[ RcIcO RdCdH R\\RjRj[ RVWSZ RTIYK RROWP RcRcV',
	    'PoRFlF RUJkJ RWO`O RaOkO RZCZH R[K[[ R_Q_W R_G_J RdCdH RfKf[ RjQiYlWlY RXRWY RUJUSSZ RbQaX',
	    'PoRElE RUKlK RXMbM RXUaU RXYcY RXMXZ RZCZH R]M]Q R]U]X RgHjI RiNe[ RXQaQaU RUKUTSZ RdCdLf[k[lX',
	    'PoRElE RTIkI RUKiK RRMlM RVRhR RVUhU RVXhX RVPVW RZCZG R_G_M RdCdG RVPhPiW R[YR[ RcYl[',
	    'PoRElE RTIjI RROlO RZWdW RVZiZ RVQV[ RZCZG RZTZW R_G_O RZTdTdW RdCdG RVQiQi[ RXKZM RfKdO',
	    'PoSFlF R[NcN R[RcR ReRjR Rb[k[ RUKUS RZCZH R[K[R RcTcZ R[KcKcR RdCdH ReKjKjR RlVlZ RYQSS RYJTK R[VXYR[',
	    'PoRElE RbMlM RVN]N RZCZG RdCdG RfUk[ RgUa[ RYHSN RYQYV RTRUW RcOgW RYI^K RcLeU RiLgV R^Q\\Y RcHaP R_XSZ',
	    'PoRFlF RZOfO RZRfR RZUfU RZXfX Re[k[ RSMS[ RZNZY RZCZH RaMaX RdCdH RZJkJk[ RUIXL R`LbO R[MVR',
	    'PoRElE RUM^M RdNiN RSQ^Q R[[k[ RSJSM RZCZG R[Q[Z RdLdQ RdCdG RSJkJkM R^GaJ RlWk[ RaThW RTZR[ RaRjRfV_Y RVQUY',
	    'PoRFlF R]LjL R]OjO R]YhY Rf[j[ RROVOVY RZCZH R]I]O R]T]Y RdCdH R]IjIjO RUIXL R`P[U RdT`W RcUhW RZWUY R^SkRj[',
	    'PoRElE RbMkM R\\[^[ RTOT[ RYIY[ RZCZH RTO^O^[ RdCdH ReUk[ R\\J_M RgTa[ RUJRN RbNfW RjNfW RdI`R R[R\\W RVRUW',
	    'PoSEkE RRL]L RRTkT RYIYQ RZCZG R_Q_[ RdCdG ReMaP RXKSQ RbH`L RXM]O RaUlZ ReJcO R_S\\URZ R\\HTI RaJkJiQeQ',
	    'PoRFlF RWIbI Re[i[ RZCZG R]W]Z RdCdG RWMfMeQ RWQhQhT RdVfX RgJjM RVJSN RaVbY R^J^L RYVW[ RVUkTj[ RVOUW RhHbI',
	    'PoRFlF RRK^K R`KlK R[S]S RhTjT RTNTS RXHX[ RZCZH RTN]N]S RaNaT RdCeHe[ RaNjNjS RXS\\V RfTlY ReQhV RWTRY ReS^Y RXQVV',
	    'PoRElE RRKlK RUMhM RSP]P R`SjS RUS]S RRS[S RRVlV R`[e[ RXPWS RZCZG R`P`S RdCdG RfSf[ RUHhHhM R`PjPjS RWW\\Y',
	    'PoRFlF R]LkL RTNZN R[PlP RTTZT Rd[g[ RW[Z[ RTIZIZ[ RZCZH RdCdG R^I_K RfPjT RaO\\T RhIgK RcJ_R RcSaX`Z\\[ R^ThTh[ RTITTS[',
	    'PoRElE RRNlN RVWhW RVZhZ RWSW[ RZCZG RUJ_I_R R_S_Z RdCdG RUTiSh[ RXKZL RfKcN RaOkT R]ORT RaI\\I RhHdH_I',
	    'PoRElE RTL\\L RaLjL RTO\\O RaOjO R\\YcY Re[j[ RTIT[ RZCZG R\\S\\Y RTI\\I\\N RaIaN RcVcY RdCdG RaIjIj[ R^PaS ReRgV RXSWX',
	    'PoRFlF R\\MjM R\\PjP Rh[k[ RZCZH RZSZ[ R\\I\\P RdCdH R\\IjIjP RZSkSk[ RVWSZ RSIXK RROWQ RcIdW]X RfUgW`W',
	    'PoSFlF RUJlJ RXPaP RXSaS RXVaV RXMXV RZCZH RXMaMaV RdCdH R^W`Y RgHjI RZWWZ RfVb[ ReShZ RjNeX RlXk[i[ RcJeW RUJUSSY',
	    'PoRElE RUM]M RaMjM RVRiR RVUiU RRXlX RUIUL RVOVU RZCZG RUI]I]L R_O_[ RaIaL RdCdH RVOiOiU RaIjIjL',
	    'PoRElE RRHlH RTNjN RUSiS RUViV RRXlX RTKTN RUQUU RZCZG R[H[N R_V_[ RcHcN RdCdG RUQiQiU RTKjKjN',
	    'PoSFlF RWKkK RWNjN RWQjQ RWUlU RWJWU RZCZH R[W\\[ R`J`U RdCdH RgWl[ RWWS[ RXIRO RaWc[',
	    'PoRFlF RW[[[ Rc[h[ RWSW[ RZCZH R[Y[[ RdSd[ RdCdH R[JZOdNdO RhXh[ R\\T]X RWPYS RcQgR R^H`K RfJjN RiTlZ RVJUN RTURZ R`U_Z',
	    'PoRFlF R_KhK RSOkO RXU]U ReUkU R^[l[ RTST[ RXJXO RXPXZ RZCZH R_H_O RaSa[ RdCdH RePe[ R^YR[',
	    'PoRElE RVLgL RVOgO RSV\\V RaVkV RSZ\\Z RaZkZ RSRS[ RVIVO RZCZG RSR\\R\\[ RaRa[ RdCdG RVIgIgO RaRkRk[',
	    'PoRFlF R^MfM RZSbS RZWbW R_[b[ Rg[j[ RYCYH RZPZ[ RZPbPb[ RfQfX RjPj[ RTIXK RdCcHdKlN RVUTZ RbH^LXN RROWP',
	    'PoRFkF R]IcI RUJ_J RRKkK RYQeQ RYYdY Rf[i[ RUSU[ RYVYZ RZCZH RdCdH RYVeVeY RXNfNeQ RUSiSi[ RdMlP R]IXNRQ RYMYQ RgH_I',
	    'PoSElE R\\KgK R[NlN R_VjV R_ZjZ RYNYY RZCZG R`R`[ RbHbN RdCdG R]SjSj[ RYITN RXPRV RfPhQ RjIeObPZT RW[T[ RTIWLYT',
	    'PoRElE RSIkI RRTlT RTWkW RZCZG R_I_[ RdCdG RXP\\Q ReKjM ReJbM ReNaR RYJVM RYNUR ReOjR RYK]M',
	    'PoRElE RTL\\L RbLjL RTO\\O RbOjO R[TcT R[WcW Rf[j[ RTIT[ RZCZH R[R[X RTI\\I\\O RbIbO RdCdH RbIjIj[ R[RcRcZ`Z',
	    'PoRElE RTLjL RVRhR RVThT RVWhW RTHTL RVOVW RZCZG RdCdG RVOhOhW RTHjHjL R[H[L RbHcL RcYl[ R[YR[',
	    'PoRElE RTHjH RVN\\N RbNhN RUP\\P RbPiP RVShS RRVlV RTZjZ RSKSO RZCZG R_H_Q RdCdG RSKkKkO RdWk[ RZVXZ',
	    'PoRElE RSK^K ReNiN RRN^N RSS^S RRW^W RUPUS RXIXN RXSX[ RZCZH R`J`[ RdCdH RdTj[ RiUb[ R[PZS RcQeV R`JjJiN R`QjQgW',
	    'PoRElE ReTjT R\\WjW R\\ZjZ RS[W[ RXOXY RZCZH R\\P\\[ RdCdH R^MgMfT R\\PjPj[ RaJgJlN RaR^U RYISP RXQRV R^JZN RSIWMXU RaMaT',
	    'PoRElE RZIkI R\\OiO RZRkR RSUlU RZCZG R\\I\\R R_R_[ RdCdG R\\KiKiO RXNTS RRKWL R_UjZ R_UTZ RTHYI',
	    'PoRFlF RaKiK RTO\\O RXT\\T R`ZjZ RTJTO RTRTZ RXOXY RZCZH RTJ\\J\\O RaSa[ RdCdG R_TkTj[ RbJ]N RaLeQlT RhLcR]T R]YRZ',
	    'PoRElE RUIiI RSLlL RSO]O R`OkO RUR]R R_RlR RSRZR RRUlU Rc[l[ RXMXR RZCZG R_G_L RcUcZ RdCdG ReMeR RZVXYS[',
	    'PoRElE RSIkI RUNiN RUQiQ RX[f[ RULUQ RZCZG R_G_T RdCdG RULiLiQ RhWl[ RSThTjV RfYf[ R]VaX RTWR[ RXWX[',
	    'PoRElE RWKgK RTP]P RaPkP RURiR RRTlT Rb[f[ RTMTP RWHXK RZCZG RTM]M]P RaMaP RdCdG RWHgHgK RaMkMjP RWWhWf[ RXTWX',
	    'PoRFlF RRO[O R^PjP RgYkY RXJX[ RZCZG RdLd[ RdCdG R]LjLjP RWP[S R`IaK RgIeL RWNTV RdS_X[Z R]P]TkTkY R[IRJ',
	    'PoRFlF RbJkJ RTM\\M RbMjM RbQlQ RZ[d[ RZCZG R[V[[ RbHbS RdCdG RgVlZ RVWR[ RdXd[ RSJ\\I\\PXT R\\TaU RXPRQ R[PUQ',
	    'PoRElE RRR\\R R_TkT R_[k[ RXRX[ RZCZH R_M_Z RdCdH ReNeT R^OkNkU RZO\\R RSKWO RaKgKeP RlXk[ RWITL RYMTR RaJ]O R[T\\X RSTSZ RbH_M RZJZL',
	    'PoRElE RVKhK RVNhN RRQlQ RUS]S RUV]V RUZ]Z RRZZZ RUQUY RVHVN RZCZH R]Q][ RdCdH RVHhHhN RaTdXj[ R_SjTfX_[',
	    'PoRElE RUR\\R RfSiS RW[[[ RZCZG RVO\\O\\R RdCdG RgHcJ RjKfM RTI]IWN R`UaW R`NeMlP RUKYLRP RaHfNfR RcYk[ R`N_S R_UhUeXbZ][ RURUV\\U[[',
	    'PoRElE RbOjO RRVjV RZCZG R_V_[ RbIbO RdCdG RbIjIjO ReTlW RdXkZ RVR^V R]QXS RdQZV RYXS[ RWJUM^O RSJ^JZOSQ',
	    'PoRElE RUIjI RRLlL RVRiR RVUiU RVXiX RVOVX RYJYN RZCZG R_G_J RdCdG ReJeM RVOiOiX R[YR[ RcYl[',
	    'PoRElE RVKhK RRNlN RVRiR RVUiU RVWiW RVPVW RVIVK RZCZG R_G_N RdCdG RVIhIhK RVPiPiW RcYl[ R[YR[',
	    'PoRElE R`RjR R_XlX RSNSQ RZCZG R`T_X RdCdG RePe[ RSNkNkQ RWKYN R\\TVZ RhJdN RXOSU RTI_J^M RWR]R[W RVTYWS[ RbH^I ReH`I RiHbH',
	    'PoRFlF RRJ_J RaMjM RaQjQ R`VkV RU[X[ RZRZY RZCZH RdCdH ReIeV RXJSO RXOTS RYSTX RbJ`O RWLYQZW RYJ[S R_MZP\\T_WcYl[',
	    'PoSFlF R`MlM R`QkQ RTR]R R`UkU R`ZlZ RV[[[ RTMTR RZCZH RSK\\K[[ RaLa[ RdCdH RgMgY R[RSY RaI_O RfJgN',
	    'PoRFlF R\\JlJ R^PjP R^SjS R^WjW RZCZG R^M^W RdJcM RdCdG R^MjMjW RgYl[ RXKSN RZOXQRS R[UWYRZ RaX[[',
	    'PoRFlF R]KlK R]PiP R]SiS R^ViV RWPW[ RZCZG R]M]S RdCdG R]MjMjS RXISM RXMRS R^IZM RaS_UYW R]VcYj[ RhVcY\\[ RdYZ[',
	    'PoSFlF RTJkJ RVOaO RWV`V RWQWW RZCZH R`Q`W RdCdH RhWl[ R[O[WW[ ReVa[ RXKYM R_K^N RdKbS RcOkOjS RfQeX RfOgW RTJTSSZ',
	    'PoRElE R\\LiL R\\OiO RYRlR Re[i[ RZCZH R\\I\\O RdCdG R\\IiIiO R^RYW RaT[[ ReVa[ RTIXK RRNVP RVTTZ R\\UjUi[',
	    'PoRElE RVKkK RRPlP RRUlU RXIXU RZCZH R\\K\\U RbKbU RdCdH RgKgU RhWl[ RWWS[ RXJSN R\\W\\[ RaWc[',
	    'PoRElE RTJjJ RSMlM RVSiS RVUiU RTXjX RR[l[ RVPVU RZCZH R_H_J R_P_[ RdCdH RVPiPiU RYJ[M ReJcM',
	    'PoSFlF R^NgN R^SiS RSS[S R`[d[ RZCZH RdCdG RdRd[ RhVlY RiPlR RiKfN RWISN R[KSS RdI_N RaV^Y RYP[T R[V[Y ReO^R RWVWZ RSVS[ RYMRN',
	    'PoRElE RRLlL RUOiO RUViV RdXiX RZCZG RUPUT[R[S RTXZX[[ R_G_[ RcXc[ RdCdG RUIiIiO RdPdSiRi[ RURTZ',
	    'PoRFlF RWM]M RaMhM RWQhQ Re[i[ RWJWQ RZCZH RdCdH RaJhJhQ ReVgX R\\V]Z R`VbZ RXVV[ R[QXTRW R]IVI RWUjTi[',
	    'PoRFlF R_JhJ R^QhQ R]UjU R]WiW RZ[l[ RSIS[ RZCZH RcQcZ RdCdH R^S[V RSJYIVO R`JZL R^KdNlP RgKaNZP RWPYTYWVW',
	    'PoRElE R]MhM R]QhQ RY[l[ RZCZH R[S[Z R]J]Q R`S`Z RdCdH ReSeZ R]JhJhQ R[SjSjZ RTIXK RRNVP RVUTZ',
	    'PoRFlF RRO\\O R^RkR R^UkU Rh[k[ RXJX[ RZCZG R^N^[ RdCdH ReIeN R^NkNk[ RWO\\T R_JaL RjIhN RXNSW R[IRJ',
	    'PoRFlF RbPlP RTP_P RTS_S RRW_W RTLTS RYLY[ RZCZH RTL_L_S RdCdG RhPh[ RUIWK R\\I[L RbJbTaZ RkIcJ',
	    'PoRFlF RaLjL RSL]L RSO]O RaOjO RZUdU RZXdX Rf[j[ RSIS[ RZCZH RZRZY RSI]I]O RaIaO RdCdH RZRdRdX RaIjIk[',
	    'PoRElE R_MjM R_QjQ R[[l[ RZCZH R^T^[ R_J_Q RbTb[ RdCdH RfTf[ R_JjJjQ R^TkTk[ RWLRP R\\MSV R[USV R[YR[ RYPRQ',
	    'PoRElE R_RiR R_UiU RZCZH R_O_[ RdCdH R_OiOiU RVRYS RcMdO RYVS[ RkVfX RcI^O RdIfLlN RcWkZ RWJRS R[OWX RVM[MYV RdY^Z',
	    'PoRElE RUHiH RVM\\M RbMhM RVO\\O RbOhO RSKSN RZCZG RdCdG RSKkKkN R]R`S R[Wc[ RWUgU_Y R_H_OdRlT R_OYRRT',
	    'PoRElE RSJ\\J R^JlJ R_PkP R_SkS R_WkW RS[W[ RWPW[ RZCZH R_M_W RdCdH ReJdM R_MkMkW R[KWM RTLZO RgYlZ RRP]P[T RbX\\[',
	    'PoRFlF RZNbN RdNkN RcQkQ RZQbQ RZUbU R[[l[ RZCZH RZJZY RZJbJbN RdCdH RdIkIkN RSHVL RdShUkY RRPWPWXRZ RjSgWbY RVY[Za[',
	    'PoRFlF RRJlJ R_OkO R_RkR R_VkV R_ZlZ RZCZG R_G_J R`N`[ RdCdG RfMfZ RTNXR RXKUO RZOTU RaK^P RfKeO R[SZWWYS[ R[TTU',
	    'PoRElE RUM^M RUR^R RUU^U RUY^Y RSY[Y RUIUM RUOUY RZCZG RRO_O^[ RUI^I^M RdCdG RhIjK RjQiTcY`[ ReIeNgX RlNaP RlWk[i[',
	    'PoSElE RUI]I RUM\\M R_MlM RUP\\P RRS]S R^TlT RZCZH RbHbT RdCdH RhHhT RgVl[ RbW_[ RUIUSTZ R[V\\ZRZ',
	    'PoRFlF RTJ\\J RTM\\M R^NlN RTP\\P R_QjQ RRS]S R^TkT R]XlX RZCZH RdCdH RdId[ R_KjKjQ RTJUSSZ RZU\\YRY',
	    'PoRElE RULiL RUNiN RUQkQ RXShS RUVlV RUIUN RYVY[ RZC[G\\N RbGbN RdCdG RUIiIiN RiXbYl[ RUQTY R]ZW[ R`ZZ[',
	    'PoRElE RTHjH RVN\\N RbNhN RUQ\\Q RbQiQ RUWiW RUZiZ RSKSO RUSU[ RZCZG R_H_Q R_S_Z RdCdG RUSiSi[ RSKkKkO',
	    'PoRElE R^NiN R[QlQ RZCZH RcKcM RdCdG RWHVTYW RhJkN R_W[[ R^J\\N R_TiTfX_[ R`TcXgZl[ RTMRR RaN^X RZLWOUX RiH^J',
	    'PoRElE R\\QiQ RXZlZ RZCZH R[T[Z R\\J\\Q R`T`Z RdCdH ReTeZ R\\JiJiQ R[TjTjZ RTIXK RbL^O RbMfO RRNWP RVUS[',
	    'PoRElE RXOfO RRVlV RUIUT RZCZG RYL_L_S RdCdG RUIiIiT R_UbY RdMaO R]OeS R`O\\QWS RcYl[ R^U[YR[ RdK\\K',
	    'PoRFkF RbMkM RTM^M RTP^P RRS_S RX[\\[ RTKTP RZCZH RTK^K^P RdCdH RVWR[ RbNeUhYlZ RcI`Q RiOfVbY_[ RWSUY RVV]V\\[',
	    'PoRElE RYJlJ R[PiP R^XfX Rf[k[ RYSY[ RZCZH R[M[P R^U^Y RdCdH R^UfUfX R[MiMiP RYSkSk[ RTIWJ RRNVP RVTS[',
	    'PoRElE RZJlJ R[PjP R[SjS RYUlU Rb[f[ RZCZG R[M[S RcHcS RdCdG RfSf[ R[MjMjS R]W`Z RTHXJ RRNWO RgGjH RWTTZ',
	    'PoRElE R\\HkH R_KjK RRLYL R\\NkN R[VlV Rd[h[ RZCZG R`N`S RdCdG RhTh[ RUUYY RWWR[ RdNgQlS R^WbY RXQUY RjOgQ RYMVX RUHSU R\\H\\N[S ReR]S',
	    'PoRElE R_HeH RSMSO RZCZG RdCdG RSMkMkO RWJYL RTI_I_M RfOjR RgJdL RWPTS R]NaO R[TSX RZOZRdQdR RbXU[ RYV]XaYl[ RhGbH RYVfVaX]YR[',
	    'PoREkE RZNbN RYQbQ RWOW[ RZJZM RZCZH R^H^M RbJbM RgVl[ RhVb[ RWHSM RXNRS ReQiY RjPfX R[SaSbX_X ReMgW ReMkMhW RdCdHcR R[SZZ',
	    'PoREkE RURiR RVWhW RVZhZ RUNUR RVUV[ RZCZH R_N_R RdCdH RVUhUh[ RUNiNiR RYO[Q ReOcQ R_GhL RZKbJlL R_GXKRL',
	    'PoRFlF R_IhI RRLkL RUOkO RWR`R RXHXL RZCZG R_H_L RdCdG RdVh[ ReNhN RXTVX RgRfU`[ R\\R\\XW[ RaNdW RlXk[i[ RUQTY R_U[YZW',
	    'PoSElE RTQ`Q RTU`U Rf[j[ RULUU RXWXZ R[M[U RZCZH RSM`M`U RdJdV RdCdH RjIj[ R_WaZ RWIRN RVJ\\JZN R[W\\Z RTWS[',
	    'PoRElE RSI[I RVRhR RVUhU RVWhW RVPVW RZCZG RdCdG RVPhPhW R^JkIhK R`G\\K RcJaM\\N RcJfMkN RcYl[ RZMRN R[YR[',
	    'PoRElE R\\NcN R\\QcQ RZCZH R\\J\\Q R_H]JcJcQ R]S\\ZcYc[ RdCdH ReIe[ RRLVP RXVS[ ReJlIiO RVHTM RXLTT RZRWY RbT\\V RjPlTlWhW RYSRT',
	    'PoRElE RSJ[J R\\[`[ Rd[k[ RSQS[ RZCZG RSQ`Q`[ RdCdG RdId[ RlWl[ R_J]L RWW\\Y RTM_MZP RYQVTZU]VZXVZ RWO[P R_IYI',
	    'PoRElE RULjL R`TiT R\\TeT RSOSR RUHUL RWSW[ RZCZG RdCdG RUHjHjL RSOkOkR RYQSV R_QbW RYUaTdYi[ R[H\\L RbHcL RbQgR RhVdXZ[ RkXk[i[',
	    'PoRElE RTIjI RRMlM RVRhR RVUhU RVOVU RZCZG R_G_I RdCdG RVOhOhU RhWk[ RVWS[ RdKdM RZJYL R]VaX R[WZ[eZd[',
	    'PoRElE R_JjJ R]NlN RRO\\O RfRlR RXJX[ RZCZH RdCdG RdIdN RXQ\\U RaO^R RcR_UcXj[ RXNTW RfNfR RaTiTfXbY\\[ R[IRJ',
	    'PoRFlF RSHlH RVMiM RVOiO RSRlR RUViV RUYiY RR[l[ RUTUY RVKVO RZCZG R_K_O R_T_Y RdCdG RVKiKiO RUTiTiY',
	    'PoRElE RZJ_J RSUkU RZHZL RZCZG R_Q_[ RdCdG RVNYP RXRZS RfRdS RWJSN RaLjP RUL^L[OTR RaIjIfN`P R_U[VSZ R`UfYlZ',
	    'PoRElE R[JlJ R\\MiM RZCZG R\\Q\\T RcOcT RdCdG R\\HiHiM RZOZPlOlQ RRNWO RTHXJ RWSS[ R\\QiQiTfS RZViVdY\\[ R^WcYl[ ReYY[',
	    'PoRElE RTIjI RRPlP RZXdX RVZiZ RVRV[ RZCZH RZTZX R_G_O RZTdTdX RdCdH RVRiRi[ RXL\\M ReJbN RYJUN ReLiN',
	    'PoRElE RUJkJ RWMjM RVRlR RXUiU RZCZH R]L][ RcLc[ RdCdH RXPiPiU R^GaI RcUfXkZ R]UZYUZ RUJTTSZ',
	    'PoRFlF R_WfW RgYjY R[[l[ RZCZH R[Q[Y R_S_X RdCdH R_SfSfW R^IhIhP R^I\\PkPkY RUJXL RRPVPVWR[ RVX][ R^MdMdQ',
	    'PoRElE RYLeL RWNgN RYTeT RTZjZ RTIT[ RYQYT RZCZH R_T_Y RdCdH RYQeQeT RTIjIj[ R]ThX R`TWX RgTcV R_K_O',
	    'PoSElE R[JiJ RYMkM RZQjQ R[TiT RYWkW R\\[l[ RZCZG RbHbM RbQbY RdCdG R^N_Q RfNdQ RTIWJ RSMWN RRRWRWXRZ RYZa[',
	    'PoRElE RTL]L R`MlM R`QkQ RRQ]Q R`UkU R`ZlZ RZCZH RaKa[ RdCdH RfKfZ RXKXT]X RWWR[ RUISO RaI]Q RfHfN RXOWW',
	    'PoRElE RSI\\I R^LjL R^OjO R\\RlR Rg[j[ RZCZH R^I^O RdCdG R^IjIjO RVRYUS[ RaR\\V RhVb[ RWISS RUN[OWX RcT`X\\Y R^UkUk[',
	    'PoRElE R_KkK RSP[P R]QlQ R^UkU RSY\\Y RSKS[ RZCZH RSK[K[P RST\\T\\Y ReCeK ReQe[ RWHVK RaLbP RhMgQ',
	    'PoRFlF R_KkK RTO]O R^QlQ R_UkU RUY]Y RTJTTU[ RZCZH RTJ]J]O RUS]S]Z RdCdH ReIeK ReQe[ RbMbP RhLgP RTPRZ',
	    'PoSElE RULlL RcNkN RZPhP RWRkR RYTiT RYViV RY[i[ RYXY[ RcKcN RdCdG RYXiXi[ RYIcI`K R[CZHWKRM R\\MWO RVLUSTY',
	    'PoRElE RSJ\\J R_RiR R_UiU RZCZH R_O_[ RdCdH R_OiOiU RURYT RdMdO RYUS[ RcI^O RdIfLlN RcWkZ RWJSS RUO[OWX RjVfX RdY^Z',
	    'PoREkE RZMdM RVT]T RaThT RVOVT RZCZH RVO]O]T RaOaT RdCdH RaOhOhT RYVUZ RdV_[ R`IlN RcWiZ RYW]Y R]JRN R_H]H',
	    'PoRFlF R]JlJ R_PjP R_TjT R_WjW RZCZH R_M_W RdCdH RdJcM R_MjMjW RWIWT\\W RVWR[ RgXl[ R[LYP RbX\\[ RTLSR RWNVW',
	    'PoRElE RXKfK RXNfN RXPfP RbSjS RRV\\V RXIXP RZCZG RTKTS\\S\\[ RbSb[ RdCdG RXIfIfP RjKjS RbVkVk[ RWWT[',
	    'PoRFlF RTQ^Q RaSkS RTU^U R_VlV R[[^[ RZMZU RZCZH RSN^N^[ RdCdH RfPf[ RWHULRN RVJ\\JYO RdKaO`U RjNgN R`IkIjN RTMTTSZ',
	    'PoRFlF RRK^K R_LlL RSN]N R_QjQ RTT]T RTQTT RYIYN RZCZH RTQ]Q]T RdCdH ReIeP RUUVX RaQcUfYl[ R[VZY RjSdX^[ R^XRZ',
	    'PoSElE RSJ]J R]MaM RfMkM R]QaQ RfQkQ R\\UaU RfUkU R[[l[ RZCZH RbIbZ RdCdH RfIfZ RURYUT[ RWJSS RVO[OWX',
	    'PoRElE RULjL RVUdU Rd[i[ RVQVV RZCZG R]N]Y RVQdQdU RdCdG RUHjHjL RUY]YcXeX RZLTR RUHUL R\\H\\L RcHcL RWOjOjUi[',
	    'PoRElE RTIkI RTPjP RYSkS RZCZG R\\I\\O R[W\\[ R_G_I RcIcO RdCdG RTLjLjP RdWfY R`WaZ RWWV[ Rj[f[ RYPXVkUj[ RTITTR[',
	    'PoRElE RRIlI RWNhN RURiR Ra[k[ RWKVN RZCZG RbRbZ RdCdG RWKhKhN RSPSQkPkS RZRXTSW RWU^U\\XV[ RVV[XS[ RjUaW',
	    'PoRElE RULjL RURiR RZCZG RdCdG RUHjHjL RSOSPkOkR RbRbZlYl[ RYRWTRV RVU^U[XU[ R[H\\L RbHcL RUHUL RUVZXR[ RjTaV',
	    'PoRElE R\\JlJ R\\OlO R_TkT R_VjV R]ZlZ RSIS[ RZCZG RdCdG RdQdZ RXPYT RSIYIVO R`Q^U R`KiN RhK^N RYVVW R\\P[Y',
	    'PoRFlF RSK_K RRP_P RaPlP RST_T RYHYK RYPY[ RZCZH RdCdH RhPh[ RYSRZ RVLVO RYU^X R\\L[O RaKaS`Y RjIaK',
	    'PoRElE RaHgH RRKlK RUOiO RURiR RTTjT RRWlW RUMUR RZCZG R[X[[ RTI\\IaH`W RdDdH RUMiMiR RaXb[ RVXS[ RgXkZ',
	    'PoRElE RZMdM RZQdQ RRTlT RZCZH RZJZQ R_Q_[ RZJdJdQ RdCdH RTIXL RfNlQ R`TbVkZ R_G^I RXORQ RiJfK R^T[WR[',
	    'PoRElE R^NkN RRO\\O R^RkR R\\WlW RXIX[ RZCZG R^J^R RdCdG RgRg[ R^JkJkR RWP\\T RXMSW ReJeQbT^U R[IRJ',
	    'PoRElE RRM_M RbMlM RRT`T RZCZG RdCdG RdUjZ RTIUK RYLTQ R]I\\L RYHYO^P RUV]Z RiNeW RWRUW RcI_R RbNfUbY^[ R\\UYYR[',
	    'PoRElE RULjL RUNeN RRQlQ RXXhX RXZhZ RUHUL RYTY[ RZCZG R^L^Q RdCdG RVUhUi[ RUHjHjL RcSgT R\\H\\L RcHcL RiNaRRV',
	    'PoRElE RTHcH RRKlK RURiR RSTkT RRWlW RUMUR RZCZG R[X[[ R_H_W RdCdH RUMiMiR RYN[P RaXb[ RVXS[ RdNbP RgXkZ RhGdH',
	    'PoSFlF RRN`N RaOlO RSS_S RTV_V RV[_[ RR[][ Re[i[ RWJWN RYSYZ RZCZH R[J[N RdCdH RiJi[ RTJTL RVOWS R_J]L R]PZR RbRdV',
	    'PoRElE RVLhL RWShS RWVhV RSISL RZCZG R_L_O RdCdG RRQhQhX RSIkIkL RZMTN RZZR[ RXPXXfYl[ ReNUQ',
	    'PoRElE R[JiJ RYMkM R[SiS R\\[l[ RZCZH R[P[S R`S`Y RbHbM RdCdH R[PiPiS RSIWM RUXR[ R`TjX RbSYW RjTeV RRQWQWX[Za[',
	    'PoRElE RZNdN RRTlT RSVkV RZ[_[ RSISL RVPVT RZCZG RZKZN R\\P\\T R_G_I R_V_[ RbPbT RdCdG RVPhPhT RSIkIkL RWLUN RfLiM R]JaK',
	    'PoRElE RSIkI RUKiK RWPgP RURiR RRTlT R^WlW RTZ\\Z Rd[h[ RTVT[ RZCZG RTV\\V\\[ R_G_K R_P_R RdCdG RhUh[ R`XbZ RRMkMkP',
	    'PoRFlF R_MkM R_RkR R_VkV R]ZlZ RV[Z[ RZCZH R_I_R RdCdH ReIeZ R_IkIkR RWLXM RTMUO R\\MRR RZRRU R[TYVRY R[IRK RZQ\\W[[',
	    'PoRElE RTHjH RVM\\M RbMhM RUP\\P RbPiP RRRlR RSKSN RUUU[ RZCZG R\\U\\[ R^R]U R_H_Q RcUc[ RdCdG RUUjUj[ RSKkKkN Rg[j[g[',
	    'PoRFlF RZIkI R[OjO R\\RjR RYUlU R_[b[ RZCZH R`I`O RcUc[ RdCdH ReIeO R[LjLjP RgWkY R\\XXZ RTIXK RRNVP RVUS[ R[L[P',
	    'PoRFlF RSHkH RTKjK RVOhO RVShS RUW_WiX RR[l[ RSQST RZCZG R_G_K R_V_[ RdCdG RVMVNhMhO RSQkQkT R[SXV RcUjV',
	    'PoRElE RRIlI RWWhW RZCZG R_G_I R_M_Q RcLcP RdCdG RWShRh[ R\\J_M RhMlQ RbJ`L RVLTPRQ RUZS[ RSL[LZPWQ RjKcL RWRVY RhPbP',
	    'PoRFlF R[QiQ R[TiT RXVlV RZCZG R[N[S RbNb[ RdCdG R[NiNiS RZK]L R`JcL R]IZJ RcI`J RiIeJiL RTHXJ RRNWO RbVl[ RWSS[ RbVXZ',
	    'PoRFlF RTIkI RYQ`Q RbTkT RXWiW RU[l[ RYOYT RZCZG R[I[O R_G_I RVU[T`U`[ RcIcT RdCdG RTLjLjO RTITTR[ RTOjPbR',
	    'PoRFlF RRI^I R`IkI RSR^R RSU^U Ri[l[ RSMSQ RWIWQXY RZCZH RZIZQ RSM^M^Q RdCdH RlXl[ RbIcRfV RbNiNiZ_[ RbQaVa[ R\\YRZ R^YWY',
	    'PoRElE RRHlH Rf[j[ RVLV[ RZCZG R_H_[ RdCdG RTMjMj[ RdVfY RXV\\VWZ RfNaS RfUaZ R\\NWS RbOfQ RXP\\Q ReJlM RYJRM',
	    'PoRElE R_JjJ RTN]N RTR]R ReTkT RRU^U Re[l[ RTITN RZCZH RdCdH RfPfZ RaVdZ RbLgN RXW\\Y R_PkPjS RUOST RiKdM RXWUZR[ RaS_Z R\\JTK RXQXX',
	    'PoRElE RSPSS RZCZG RdCdG RSPkPkS RjIhK RgSdV RUISK R]I[K RaI`K RYSUV ReKkN RXK]M RYHWMSN ReHcM_N R_R`VdYl[ R_Q]VZYR[',
	    'PoRElE RSIkI RWMgM RYSdS RgTjT RRVlV RTOTT RYQYT RZCZG R_T_[ RdCdG RYQdQdS RWKWLfKfM RTOjOjT R_VdXlZ R_VYYRZ',
	    'PoRElE RUK]K RUN]N RVP]P RRPZP RUIUP RZCZG RRI^I]Q R`S`[ RdCdG RbKeNlQ RaSeWlZ RYRSU R`JkJgN`Q RZWRZ RiTdV RdRZT',
	    'PoRFlF RRK`K RUP]P RVX\\X R[[_[ RSSS[ RUMUP RVUVY RVU\\U[X RUM]M]P RSS_S_[ RcCcH RhWl[ RdWa[ R[CZHZK RfRjY RfRcZ RcLkLjQ RcJ`Q RfNeW',
	    'PoRElE RSIkI RXKeK RXMeM RRPlP RXTaT R[[_[ RZCZG RdCdG RfHePgRlT RUW`WgViV RdXj[ RXHYPVSRU R_RXT RYXT[ R_W_[ RbSTW',
	    'PoRElE RTLjL RRO`O RTQ^Q RTT^T RTZ^Z Re[j[ RTHTL RTWT[ RZCZG RTW^W_Z RdOdV RdCdG RTHjHjL RjMj[ R[H[L RbHcL',
	    'PoRElE RUKiK RVNhN RRPlP RWUgU RUXiX R[[_[ RSISL RZCZG RZJZQ RYS_S_[ RdCdG RdJdQ RSIkIkL ReRlU RYRRU RcR\\S',
	    'PoRElE R]JbJ RUNZN RdNiN RURZR RdRiR RRVlV RUJUV RZCZG R]Q]V R]H]NbNbV RdCdH RdJiJiV RcWk[ R[XS[ RZIUJ',
	    'PoRFlF RSNYN R_PfP RSSYS R[TkT RSXYX RSJSZ RSJYJYX RZCZG RdCdG R\\K_N RfXj[ ReLjQ RaL\\Q RhJfL RjLhO RdIfN RbVgX R\\JbJ_N RbV_Y[[ RcPaX',
	    'PoSFlF R^KkK RSN\\N R]PlP RRR]R R`VjV R`ZjZ RXHX[ RZCZH R`S`[ RbHbP RdCdH RhHhP R`SjSj[ RWS\\W RVURY RXPUW R[JTK',
	    'PoSElE RRN[N R^PkP R_VlV RXIX[ RZCZG R^J^T_[ RcSc[ RdCdG RgSg[ R^LkLkP RWP[T RWMSV R_SlSl[i[ RWKVQ R[IRJ R^O\\Y RjH_J',
	    'PoRElE RVRjR RVTiT RVWiW RVZkZ RWQW[ RZCZG RTI]I]N R_Q_Z RdCdG R`IjIiO RXOSU R[LTO RfI`L RhLbN R[ITL',
	    'PoRElE RS[j[ RZCZG R_G_I R_R_Z RdCdG RZQePdQ RVTiTiW RVTVWhXj[ RgMkQ RWLTP R\\KaL RZMZQ RSISM ReK_NSR RSIlIlM',
	    'PoRFlF RSI^I RbLlL RSO^O RbQkQ RSR_R RR[l[ RSISR RUUU[ RYIYL RYOYR RZCZH R\\U\\[ RSL^L^P RbUb[ RdCdH RUUiUi[ RcI`O',
	    'PoRFlF RRLlL RVOhO RSRkR RR[l[ RVWV[ RZCZG R[S[U R\\W\\[ R_G_R RbWb[ RdCdG RVIhIhO RVWhWh[ RaSbU RgSjV RVSSV',
	    'PoRElE RXJlJ R[McM R[TcT R[XcX RXJXW RZCZH R[M[Y R_M_P R_U_X R[PcPcT RgGjH RfVa[ RiNeX RTJTOXOSO RlXk[i[ RdCeHeOgY RTSSZ RRSXRV[',
	    'PoRFlF R_OkO R_SkS RV[Z[ R_K_S R_KkKkS RgSg[lZl[ RWKXM RTMUO RdCdHdK RaW][ R[V[Z R\\MRR RZRRU R[TYVRY RZQ\\W RbS`Y RZC[IRK',
	    'PoRElE RWWfW RVJVN RZCZG RdCdG RVOhOhT RTKiKiN RYHRK RWIbI_LYM R[VTW RbTeT RhPdQbP R^LfM RWPZQVPWTXShSdTbT_TbT R\\Yj[ RYQ\\Q_QbQ_Q\\P R_TZU RbYT[',
	    'PoRFlF R_MjM RSO\\O R_PjP R_YhY Rf[k[ RYJY[ RZCZH R_T_Y R_J_O RdCdH R_JjJjO RdUhW RWO\\T RaQ\\V ReTaW RUSRW RXNUU R`SlSk[ R[ISJ',
	    'PoRElE R`LlL R`QkQ RTR^R R_UlU RTU^U RTMTU RYNYU RZCZH RSN^N^U RdCdH ReLe[ RaIcK RiJfL RXHRN RVK]JYN RZYSY R\\XUY',
	    'PoRElE RULiL RXSfS RXVfV R[[_[ RSHSK RYOYV RZCZG R_V_[ RdCdG RVPgPfV RSHkHkK RUMXP RXWUZ RiMfP RbMkQ RdXiZ R^J[NSQ',
	    'PoRFlF R_MjM RSN\\N R_QjQ RRR]R R`XiX RXHX[ RZCZH R^S^[ R_J_P RdCdH ReJeW R_JjJjP RWS\\W RVVRY RhUiY RXQTX R^SkSk[h[ R\\JRK',
	    'PoRElE RSH^H RSM]M RVShS RVUhU RVXhX RVQVX RYHZJ RYMYO RZCZG RSJ]J]M RdCdG RSHSOZPhQhX RaHeMlO R`IkIgLaO R[YR[ RcYl[',
	    'PoRFlF RYKbK RZRbR R[[l[ RZCZH RZMZR R^H^Y RZMbMbR ReKkKjO R^RbV RTIVL RROVOVXSZ R^RYW RiSkX RgRdX RdCeHdO RVX[Z`[ RgNfV',
	    'PoSElE R_HiH R^MgM RYM^MaNhN RYSiS RYUiU R\\[f[ RYPYU RZCZG R_G_N RaP`U RdCdG RYPiPiU RhWkZ RXXVZ R_VcX R\\W\\[ RUJlJkM RUJURSZ',
	    'PoRElE RSJ^J RRO_O RhOlO R`RjR RXHXJ RZCZG R[S[Z RdCdG RbIiIiO RlLlO R\\U_X R]QZT RUKVO RbTfYl[ R[K[O RWPRS RWTRV RXRZUWXRZ RjSeX^[ RY[V[ RbIaO',
	    'PoRElE RRHlH RTOjO RVShS RVVhV RVXhX RTLTO RVQVX RZCZG RdCdG RVQhQhX RTLfMjMjO RgJjJ RYJ\\J RWJTJ ReJbJ R[YR[ RhJeJgJ R_G_JcJ RcYl[ RVJYJWJ R_J[J R\\MVN',
	    'PoRElE RVN]N RaNhN RVQhQ Re[i[ RSISL RWLWQ RZCZG R\\V\\Z RdCdG RaKhKhQ RSIkIkL RdVgX R`VaY R^G`I RXVVZ RZQXTRV R]KVL RWUjTi[',
	    'PoRElE RRK^K RfQiQ R[[_[ RYHYP RZCZG R_R_[ RdCdG RYT[V ReTcV RXKSQ RbI^L RYM]N ReJcO R^PSU R_OfSlT RaXiZ R\\XTZ RaJjJjQ R\\HSI',
	    'PoRElE RTI]I RSK^K RZM^M RTO^O RiRlR RSRYShT RRVlV RT[b[ RYNYR RZCZG RYHYKZM RdCdG R`JiJiR RlOlR RcN_R RWKTN R`MfO R[VXZ`ZfYk[ RdHcO R^QZQ',
	    'PoRElE RTIkI RYPhP RRR^R R`RlR RUZ]Z RcZjZ RVUV[ RZKZP RZCZG RUV]V][ RcTc[ RdCdG RXLhLhP RbVjVk[ RdS_X RXRRX R\\IYLSN RcRcX',
	    'PoRElE RVLhL RVOhO RSV^V RaVkV RSZ^Z RaZkZ RSRS[ RVIVO RXRXZ RZCZG RSR^R^[ R_I_O RaRa[ RdCdG RfRfZ RVIhIhO RaRkRk[',
	    'PoRFlF RWLhL RWOhO RSV]V RaVkV RSZ]Z RaZkZ RSSS[ RWIWO RZCZG RSS]S][ RaSa[ RdCdG RWIhIhO RaSkSk[ ReQdS RXQWS',
	    'PoSFkF RZJkJ R]PhP R_WfW R\\[l[ RZCZH R[N[X R_S_W RbHbJ RbNbS RdCdH R_SfSfW RfJeM R^J_M RTIWJ RSMWN RRRWRWXRZ R[NjNjXgX RYZa[',
	    'PoREkE RZMbM RYPaP RYSbS RZVaV RYYbY RWOW[ RZJZM RZCZG R]S]Y R^H^M RbJbM RdCdG RgVl[ RhVb[ RWHSM RXNRS ReQiY RjPfX ReMgW ReMkMgW ReHcQ',
	    'PoRElE RTMjM RSQ^Q RTV]V RTX]X RTST[ RTITM RZCZG RdCdG RTIjIjM RbNbSkRkS R[O^R RWNSQ RbUbZlYl[ R[I[M RbIbM RTS]S][Y[ RjOaP RjVaW',
	    'PoRElE R\\LkL RTNZN R[PlP RTSZS RZCZH RdCdH R^T`U RfOkS RaO[T RhIgK RhSfU R^I_K ReLgQ ReWkY RcI_Q RbX\\Y RTIZIZ[W[ RcRc[a[ RTITTS[',
	    'PoRElE R[McM RRRYR R[QcQ RdRlR RRTlT RZCZH R[J[Q R_Q_[ R[JcJcQ RdCdH RXOYR RjOlR RjKhN RhHeLhNeQ RSKVNSQ RaVkZ RVHTL RXKVO R^G^J R]VS[',
	    'PoRElE RUIlI RWL`L RbLkL RiQkQ RXUkU RVXlX R\\[a[ RZCZG R[J[Q R^N^Q R`G`I RYSbSa[ RdCdG RfJfQ RiNiQ RcNaQ RXNWR RUIUSSZ RgRaS',
	    'PoRFlF R]JeJ R]NkN R\\QkQ RWVXZ RZCZH RdCdH R^K_M RcKdM RjKfN RXIRN RZKRS RZU[X R`TiTfX_[ R`TcXgZl[ RSVS[ R`O^Y RiHaI RYNRN RZP[SRS',
	    'PoRElE RUIkI RUOjO RYR`R RbUkU RYOYU RZCZG R\\I\\O R^X^[ R_G_I RcIcU RdCdG RULjLjO RiWl[ RYXW[ RdWd[ RiQbR RUITSSZ R_TXU',
	    'PoRFlF RYNlN R[WiW R[ZiZ RZCZH R\\R\\[ RbSbZ RdCdH R\\K]M RgJfN RcNeQlS RSIWJ RRNVP RUUS[ R`N^QXT RhHbI[J RbIbR R[SiSi[',
	    'PoRElE RRLaL RTN^N RSS_S RRU`U RSPTS RTITN RTI^I^N RSP_P_S RdCdG RfUk[ RjNfW RdI`P RcOgVcY_[ RcLeU RZCYSUW^Z RbMkLgU R^U[YS[',
	    'PoRElE RYIkI RZNjN R[SjS R[VjV RXXlX RZKZN RZCZG R[Q[U R_I_N RbVb[ RdCdG ReIeN R[QjQjU RZKjKjO RTHXJ RRNWP RVTS[',
	    'PoRElE R\\LjL R[NlN R]SjS R]UjU R]XjX RSIS[ RZCZG R\\I\\L R]P]X RcHcN RdCdG R]PjPjX R\\IjIjL RSIYIWO RfYl[ RXPYTYVVW RaYZ[',
	    'PoRElE RbLiL RUQ]Q RaRjR RUT]T RZ[][ Rd[i[ RUOUW RZCZG RUO^O^[ R_G_I RbIbL RdCdG R`NjNjR RSISJkIkK R^VS[ R`VkVi[ R\\IZLTM R`R`V RXWRW R^VWW',
	    'PoSFlF R^LkL RSM[M R^NkN R^QkQ R^UlU RXHX[ RZCZG R_J_U RaWa[ RdCdG ReJeU RWN[R R_K[O ReWfZ RjVl[ RXLTU R_H]N R]W[[',
	    'PoRFlF RaLlL RTL^L RWN^N RUP_P RY[f[ RYWY[ RZCZG R[PZU RTI^I^L RdCdH RhWjY R^R_T R_VaY RcNdR RfYf[ RXRUT RTWSZ RiHhUeU RTITOSU',
	    'PoRFlF RWS_S RSVaV RZZbZ RW[_[ RR[\\[ Rf[k[ RTJSO RZCZH R[S[Z RdCdH RfJfV RkIk[ RVWW[ R[ObS R^J]N R_W^Z RXKYMSO R[ORS RYISJ RbO_O R[JbJbO',
	    'PoRElE RWJlJ RZQiQ RZTiT RZCZG RZMZT RdCdG RZMiMiT RcTbZlYk[ RaHcJ RfUdX RSKUO RWRRT R`U]YX[ RbM`V RiVjXdX RWJWRVX',
	    'PoSFlF R\\JlJ RRM[M RaPjP R_WdW RfWlW R\\ZlZ RWJW[ RZCZH R\\J\\Z R_S_W RaMaQ R_SdSdW RdCdH RfSfW RaMjMjQ RfSlSlW RVOZS RWMRW',
	    'PoSEkE RSHkH RXLgL RYReR RgTjT RWUbU RRVlV RTNTT RYPYS RZCZG R_T_[ RdCdG RYPePeR RXJXKgJgL RTNjNjS R_VdXl[ R_VZYR[ RdT_T',
	    'PoRElE R_MjM R_QjQ RRR\\R R[[l[ RXRX[ RZCZH R^S^Z R_I_Q RbSbZ RdCdH RfSfZ R_IjIjQ R^SkSkZ R[P\\S RSLWO RWITL RZKTR R[T\\X RTUSZ',
	    'PoRElE R\\LiL RYNlN RSTkT RWJWR RZCZG R\\H\\L RbLbR RdCdG R\\HiHiL RiWeX RYHSN RePlQ R`TbWjZ RYWY[ RbNZQ R_TYWRY RaYX[',
	    'PoRElE RUIlI RWN_N RaNlN RaViV RV[l[ RZCZG RZUZ[ R\\K\\S RaTa[ RdCdG RfKfS R[N_Q RfMkS RfMaS R\\NWS RUIUSSZ R^JWK RiJbK',
	    'PoRElE RTI[I R^KgK RRL\\L RSO[O R]OlO RSR[R R`VjV RSZ[Z R`ZjZ RSTS[ RZCZH RST[T[Z R`S`[ RcHcO RdCdH R`SjSj[ RfQhQ RkIbR\\U',
	    'PoRFkF RSJ]J R_KhK R]OlO R`ViV R`ZiZ RZQZZ RZCZH RaRa[ RdCdH R^SiSi[ R[R\\U RjIdP RXJTN RXOTS R\\NYQTW RcIdO]S RY[V[ RXOYW',
	    'PoRElE RTJ[J RRL\\L R_LjL RSO[O R_OjO RSR[R R_YiY RSZ[Z Rf[k[ RSUS[ RZCZH RSU[U[Z R_S_Z R_I_O RdCdH R_IjIjO R`SlRlZ RdUgV RbP\\V ReTaW',
	    'PoRElE RTK]K RaKjK RTN]N RaNjN RYRfR RYTeT RYWeW RYYfZj[ RTIT[ RZQZ[ RZCZG RTI]I]N R`P`Y RaIaN RdCdG RaIjIj[ R[PVT',
	    'PoRElE R]LgL RYUlU RZCZH RdCdH R]IgIgM RTIXK RRNVP RbUfXlZ RVTS[ RbU^XX[ R]I]M RZOaOaRZRZO RbSb[ RdOkOkRdRdO',
	    'PoRElE RVNaN RUPbP RVUhU RSISK RVRVU RZCZG R[R\\U RVL_K_R RbRbU RdCdG RZWZ[dZd[ RVRhRhU RSIkIkK RhWl[ RVXR[ R]VaX ReJ_K RfL_M RdO_O RiNbO',
	    'PoRFlF RSK^K RaMkM RRP_P RaQkQ RST^T RaTkT Rg[l[ RYHYK RYPY[ RZCZH RaIaT RdCdH RhTh[ RaIkIkT RlWl[ RYSRZ RVLVO RYU^X R[L[O RcTaY][',
	    'PoRFlF RRK^K RgNjN R`RkR RSS]S R`UkU RSNSS RXHX[ RZCZH RSN]N]S RdCdH R`OkOkW R_IkIjN RWSZV RdJaN RYSRY R`O`XiXl[ RbY^[',
	    'PoRElE RTHjH RVM\\M RbMhM RUO\\O RbOiO RVSjS RVUiU RVWiW RVZkZ RSJSN RWQW[ RZCZG R_H_O R`Q`Z RdCdG RSJkJkN RXPRU',
	    'PoRElE R\\LkL RRMZM R]OjO R[RlR R[UlU R\\XkX RVHV[ RZCZG R\\I\\L RcLcQ RcUc[ RdCdG R\\IkIkL R_R`U RhRfU RaIaL RfIfL RYRRS RS[V[S[',
	    'PoRElE RSIkI RUO\\O RbOiO RTRkR RbXiX RUX\\X RR[l[ RUTUX RUKUO RZCZG RUT\\T\\X RUK\\K\\O R_I_[ RbTcX RdCdG RbKiKiO RbTiTiX RbKbO',
	    'PoRElE RWS[S RdShS RRUlU RWOWS RZCZG R_S_[ RdCdG R]O]R RWMZN R]H`I RZIZLdMgN RjOkR RgIjL RaO`R RWITL RTOSR R[XRZ R_UTZ RdOdS R_UfYlZ',
	    'PoRElE R_IhI RSLkL RYN]N RfOkO R_RlR RRUlU RTNTR RXHXK RZCZG R_S_[ R_G_K RbNbR RdCdG RfMfR R_UfYlZ R_UWYRZ R^QWR RYMYRRR',
	    'PoRElE RSLXL RZL_L RbOlO RSQ_Q RSU_U RRX`X RSISL RSNSU RSIXIXL RYNY[ RZCZG RZIZL RSN_N_U RZI_I_L RdCdH RhOh[ RkIbJbSaY',
	    'PoRFlF RfIlI ReOlO RZPdP RZSdS RYVeV Rf[i[ RWOW[ RZCZH R[L[S RZMdMdS RdCdH RiOi[ RWHSM RWNRS R\\JaJ_M_WdY R]HZM R]YY[',
	    'PoRElE R_HiH R^LgL RYM`NhN RYRhR RYUhU RU[l[ RYPYT RZCZG R]W^[ R_G_N RaPaT RdCdG RYPhPhT RUJkJkM RcWcZ RXWiWiZ RXWXZ RUJURSZ',
	    'PoRFlF R_OlO RTQ^Q RTU^U RULUT RWWWZ RYMYT RZCZH RSN^M^T RdCdH RfJf[ RhTlX RcT_X RWJRN RVJ\\JYM R]W^Z RZW[Z ReNiU RTWR[ ReNbV RjI_J',
	    'PoRFlF R_JlJ RSM]M R`PjP R`TjT R`WjW RXJXQ RZCZG RUS[S[Z R`M`W RdCdG ReJdM R`MjMjW RXL\\Q RXMSR RgYl[ R]XZZ RbX\\[ RUSTY R[ITJ',
	    'PoSElE RUJjJ R\\PiP R[RiR R]VhV RSLSO RUGVJ RWPW[ RZCZF R\\G\\J RcGcJ RdCdF R[NiNiR RUGjGjJ RZTZUkTkW RSLkLkO RYNRT R]VcYl[ ReYZ[',
	    'PoRElE R_QjQ RRR\\R R[[l[ RXRX[ RZCZH R^S^Z R_I_Q RbSbZ RdCdH RfSfZ R_IjIjQ R^SkSkZ R[P\\S RSLWO RdLgO RdLaO RWITL RZKTR R[T\\X RTUSZ',
	    'PoSElE R_JlJ RYK]K RRO^O R`PjP R`TjT RVVXV R`WjW RUKUO RYIYV RZCZH R`M`W RdCdH ReJdM R`MjMjW RTQSU RhYl[ R]R[VYYS[ RcY][',
	    'PoRElE R[JcJ RYLcL R^NcN RhPkP RZQbQ RYR_R RVSiS RRVlV R[[_[ RVLVR RZCZG RZMZQ R^I^Q R_V_[ RdCdG RfIfR RfIkIkP RVHSK RWJSO R[HZK ReWlZ RYXR[',
	    'PoRElE RRK`K RTR_R RST`T RRV`V Rf[i[ RTNTR RXXX[ RSIZIZV RZCZG RTN_N_R RdCdG RTXS[ R\\O\\Q R[X[Z RVOWQ R^X_Z RbLkLkVj[ RfHfOdW R_HYI',
	    'PoRElE RUHjH RVM\\M RbMhM RVO\\O RbOhO RUT\\T R_UkU RUW\\W RX[\\[ RSJSN RUQUX RZCZF RUQ\\Q\\[ R_H_O RdCdG ReQeU R_QkQkU RSJkJkN R`Q`ZlYl[',
	    'PoRFlF R_IkI RTN]N R`PjP RSQ]Q R`SjS RRT^T R`WjW RU[X[ RTITM RXTX[ RZCZH R`M`W RdCdH ReIdM R`MjMjW RTVSY R\\V\\X RgYl[ RcX][ R\\JTK',
	    'PoRElE RTIZI RRL[L R^NiN RSOZO R\\QlQ RSRZR RSZZZ RSTS[ RZCZH RSTZTZY RcKcM RdCdH RhJlN R^J\\N RaQ]Z R`UiTfX_[ R`TcXgZl[ RaN^X RiH^J',
	    'PoSElE RUIlI RWL`L RaLkL RiQkQ RWSkS R[ZiZ R[C[G R\\V\\[ R[J[R R^N^Q RdCdG RfJfR RiNiQ RZWiWi[ RbNaQ RXNWR R]SZWUY RUIURSY',
	    'PoSElE R]NjN RRN[N R]QjQ R\\VkV R\\XkX Rd[l[ RXIX[ RZCZG R\\S\\Y R^J^Q RdKd[ RdCdG R\\LjLjQ R\\SkSkX RWP[T R_IfIdL R`H\\L RWNSV R[HRJ',
	    'PoRElE RSO]O R_QkQ R_UkU RXJX[ RZCZH R_L_U RbXb[ RdCdH ReMeU R^NkNkU RXQ\\U RaJ\\O RaJgJeM RfWf[ RjWl[ RXNSW R^W][ RXLWR R[IRJ',
	    'PoRFlF R_IkI RTN]N R`PjP RSS^S R`SjS R`WjW RUIUM RYPY[ RZCZH R`M`W RdCdH ReIdM R`MjMjW RZU]X RXS[V RYSRY RgXl[ RcX^[ R\\JTK R\\OUP',
	    'PoRFlF R`RkR RRTZT RaTjT R`WlW Ra[l[ RSVR[ RVVWZ RZCZH RYVYY RdCdH RfPfY RdI`L R[Q^P^X[[ RWJRN RZLRT RYQZU R\\I]M RbKgNkO R]Ye[ RbKjKeN_O RXNRO',
	    'PoRElE R_JkJ RTL\\L R_MlM R_PkP RTR\\R RRUlU RTJTS RZCZG RTJ\\J\\M RTO]O]R R_R_[ RdCeGeJ ReMeS RhKgL R_US[ RcKbN R_UbVgYlZ',
	    'PoRElE R`JkJ RTM]M R_MlM R`PkP RUR]R RRUlU RUOUS RZCZG RTI]I]L RUO]O]R R_R_[ RdCeGeJ ReMeS R_US[ R_UbVgYlZ RcKcN RTISR',
	    'PoRElE RRIlI RVN]N RaNiN RTPkP RURjR RRUlU RZ[`[ RW[^[ RVKVN RZWZ[ RZCZG RZNZU RVK]K]N RaKaN RdCdG RdNdU RaKiKiN RiVcX R_UbXfZl[ R`UTX RbZ]Z',
	    'PoRElE RTH^H RWK^K RhKkK RaMiM RTP_P RY[][ Rf[j[ RTHTP RZCZG RSS]S][ RdCdG RbHhHhK R`SjSj[ RWHVK RbHaK RUL[L^O RZJYNVO RYTSV R[WSZ RfT`V RhW`Z RcNkQ RhO`Q',
	    'PoRElE R`MlM R`QkQ RTQ^Q RTT^T R_UlU RULUT RWWWZ RYMYT RZCZH RSN^M^T RdCdH RfMf[ RbHcK RVJRN RVK[JYM RZWZZ R]W^Z RiHhK RTWR[',
	    'PoRElE R`JlJ RRJ_J RUM\\M R`OkO RUP\\P RRS_S RbTjT R[W_W RbWjW RTZ_Z RTST[ RUHUS RZCZG RbQb[ RdCdG RhLlO RXSVX RdJbN R\\H\\S[W RbQjQj[g[',
	    'PoRFlF RRK^K RUN[N R_SiS RST]T RRW]W RSPST RUIUN RXNX[ RZCZG R[I[N RdMdZ RdCdG RcShX RgNfQ RdS^Y RaI_M R`NaQ RSP]P]T R`KkKkUj[f[',
	    'PoRElE RVN]N RSTXT RZT_T RSPST RSPXPXT RZCZG RZPZT RZP_P_T RdCdG RhWl[ R\\W_Z RUWXY ReV`[ RYITN RfRiY RYJ]L RfRcY RcLkLjP RUVS[ R\\UZZ RdIaP RfOhW RfOeW',
	    'PoRElE RbMlM RVM]M RTTXT RZT_T RTPTT RTPXPXT RZCZG RZPZT RZP_P_T RdCdG ReUk[ R\\W_Y RUWXY RZH_M RgUa[ RYHTM RcOgW RUUS[ R\\UZ[ RcLeU RiLfV RcHaP',
	    'PoRElE R\\IeI R[MkM RZOlO R\\SjS R[VjV R^[g[ RSIS[ RZCZG R_W_[ RcMcO RdCdG R[QjQjV RiXkZ RaVcX RiJfL RZWY[ RSIYIVO RWPYTYVVV RiHdH',
	    'PoRElE R\\JkJ RTOYO R[PlP RRSYS R`XgX R]ZjZ RTJTN RYIY[ RZCZG R]R][ R`U`X RcHcP RdCdG R`UgUgX R]RjRj[ R^MbN RhMlN RhKeN R_K\\O RUSTZ',
	    'PoRElE RTK]K RaKjK RTM]M RaMjM RRUlU RS[Z[ RZCZG RTH]H]O RdCdG RaHjHjO RTHTO\\P]Q RaHaOiPkR R[SVX RVQRS RcQ_R RXXk[ ReW`YU[',
	    'PoRElE RaJkJ RSJ^J RRO_O RaPkP RaRjR RTT]T RaUjU RTW]W RaXjX Ra[l[ RZ[][ RTQT[ RXHYJ RZCZG RTQ]Q][ RbPb[ RdCdG RaHaMjMjP RUKUO R[K[O',
	    'PoRFlF RSK_K RbLkL RRN`N RbSkS RTS^S RTW^W RaZlZ RTPTV RVIVN RYNYV RZCZH R\\I\\N RTP^P^V RdCdH RfLfZ R[X^[ ReIgK RWYR[',
	    'PoRFlF RSRZR RSXZX Rg[j[ RSLSZ RWHTLZLZX RZCZH RcWcY RdCdH R^NgNgQ R^QiQiT RhViW RiJjM ReVfY RcJcM R_W_Z R^J]N^UkTk[ R^P\\Y RhH^J',
	    'PoRFkF RUQZQ R\\QaQ RdQiQ RUWiW Re[i[ RUNUQ RUSU[ RZCZH RUNZNZQ R\\S\\[ R\\N\\Q R\\NaNaQ RbSb[ RdNdQ RdCdH RdNiNiQ RUSiSi[ R_GgK RWKdJlL R`GYJRL',
	    'PoRElE RTIjI RVN\\N RbNhN RVQ\\Q RbQhQ R\\YbY RTYYY ReYjY RSLSO RTTT[ RTTYTY[ RZCZG R\\T\\[ R_I_Q R\\TbTb[ RdCdG ReTe[ ReTjTj[ RSLkLkO',
	    'PoRElE RbIjI R^McNhO R]QkQ R[[l[ RZCZG RdCdG RSMWN RRRVRVWR[ RTIXJ RbQdTkW R[KkKjM RcS\\U RiRdT]X ReQ]S ReTeYaY RVXZZa[ R[K[QYW RbIbN',
	    'PoRFlF RTQ]Q RdSkS RRU^U RSZ]Z Rc[f[ RSWSZ RXQXZ RZCZG R]W][ RdCdG RaSgSf[ RjPkS ReJ`O RfNaS RiLcR RVORR R]K\\N RSLTN RXKXN RjUlY RbU`Z R^HTJ RjH`I RgN`O',
	    'PoRElE RUI_I RbJlJ RTP_P RZCZG R_W_[ RbJfOlR RRWgVlX RVS]V RdEbJ R]RXT RiJeO`Q RcS[V RYXR[ RdYk[ RUGTJTQ RRM`L`M RUK^K\\R',
	    'PoRElE RTIkI RTNjN RbSkS RVWlW RYNYS RZCZG R[I[N RaTa[ RcIcS RdCdG RTKjKjN R[T]V R^G`H RgTeV RaWfYl[ R`W[ZV[ RYP`QXS RiPbQ RTITSSZ',
	    'PoRElE RZIbI ReLlL RZObO RZScS RXZlZ RZCZG RZIZR R[U[Z R^I_L R^O^S R`U`Z RZLbLbO RdCdG ReUeZ R[UiUiZ RhNiR RTHXJ RRNWP RWSS[ RfHdP',
	    'PoRElE R`LjL RSM[M R`OjO RSR\\R RWV\\V RhZjZ RSJSR RSUSZ RWRWZ RZCZH RSJ[J[M RSO\\O\\S R`T`Z RdCdH ReSe[ R`IjIjO R`TjTjY R^Q^RlQlS RVHVJ R\\YRZ',
	    'PoRElE RTK]K RaKkK RTN]N RaNkN RXPfP RYTeT RYWeW Rg[k[ RTHT[ RYRYW RZCZG RTH]H]N R_O_[ RaHaN RdCdG RYReReW RaHkHk[ R^WfZ R_WXZ',
	    'PoSElE RUKjK RUNkN Rg[j[ RUHUK R[C[G R\\H\\K RcHcK RdCdG RfQfY RUHjHjK RjPj[ RbP`R RbW`X R]RbT R]Xb[ RYPXS RXVXY R]P\\SWU R]V\\YX[ RUNUUTZ',
	    'PoRElE R\\JiJ R\\MiM RYOlO RR[l[ RZCZG R\\W\\[ R\\H\\M RbWb[ RdCdG R\\HiHiM RaQ]U RfQbU RWPSU RRKVM R]QYS RUWhWi[ RTHYI RVWU[ R\\RjQjUfU',
	    'PoSEkE R_KjK R_NjN RRR[R R]SkS R]UjU RWRW[ RZCZG R]P]S R_I_M RbPbS RdCdG RfPfS R_IjIjM R]PkPkS RYO\\R RSKWO RYLTR RZT[X RWHUL RTTRY R`WeYl[ RiWdY\\[',
	    'PoRFlF R_JlJ RRM^M RaPjP RaTjT RRU^U RaWjW RXHXQ RZCZG RaMaW RdCdG ReJdM RaMjMjW RTIUK RZR\\S RXVTZ RXMSQ R\\I[L RXN]P RhXlZ RXV]Y RcY][ RXSWX',
	    'PoRElE RUKiK RUNiN RSR]R R`RkR RST]T R`TkT RSWkW RR[l[ RSOST RUHUM RXOXT RZCZG RSO]O]T R_H_M R_U_[ R`O`T RdCdG RfOfT RUHiHiM R`OkOkT',
	    'PoRFlF RSK_K RbLkL RRN`N RaPkP RTS^S RbUkU RTW^W R`ZlZ RTPTV RVIVN RYNYV RZCZH R\\I\\N RTP^P^V RdCdH RfIfP RfRfZ R\\X^[ RWYR[',
	    'PoRElE RWI]I RaJgJ RWK]K RbKgK RWN]N RaNgN RUPiP RSRkR RVXhX RR[l[ RUHUP RVTVX RZCZG RZHZP R_H_P RdHdP RdCdG RVThThX RiHiP RZY\\[ RdYb[',
	    'PoRElE R\\JdJ RTNYN R[OkO RTSYS R\\UcU R\\XcX RTITX RRIZIY[ RZCZG R\\Q\\Z R]L]N R\\QcQcX RdCdG RiIjM RiUcZ R_H`J RiQgW RlXk[ RbKaO RZVRX RfHfOhY',
	    'PoRElE RRL^L R_NkN R_RkR RgXkX RSJYIYQ RZCZG R_J_R RdCdG RhThX R_JkJkR RaX^[ RXM^P RXLUORP RWRTW\\Y RbRaY RRT]TZYS[ ReNfV ReJfRf[lZl[ R\\HUI',
	    'PoRElE RRHlH R[MbM ReMkM R[PbP RTKTQ RZCZF R[J[P R[JbJcP RdCdF ReJkJkM R_H^J R]SUW RcRkT RYLWL RWIXPRQ R[RTT RfJfQlPlQ RYV^XbZl[ RYUfUaX]ZR[',
	    'PoRElE RRI^I RaLlL RbNkN RZP]P RbRkR RSR]R RSU]U RbUlU RSLSV RZCZH RZIZO RSL]L]V RbJbU RdCdH RgIgU RgWk[ RaWc[ RVWR[ R[W[[ RbH`N RWIVP',
	    'PoRElE RZJlJ R\\OjO R[R`R RfRkR RZT`T RfTkT RVHV[ RZCZG RcPcS RdCdG R\\LjLjO RYLYO RcUfYl[ RbU[X RjVeX R^W^[ R\\L\\O R`L`O ReLeO RSMRR RdY[[',
	    'PoRElE RRJ`J RaMlM RTQ^Q RbQkQ RbUkU RTX\\X RbZlZ R[[^[ RSSS[ RTLTQ RYHYK RZCZG R^L^Q RSS^S_[ RbKb[ RdCdG RgKgZ RZU]X RYQVX RcH`P RVM[NUO',
	    'PoRElE RRH^H RhLlL RSM]M RRO_O R[[_[ RXGXR RbHaK RUX[WaWgVjW RSR]RVU RZXS[ RbHhHhL RaMePlR RSJTLWK RdYk[ R_W_[ RXKUK RSJ]J]LZK RaMjMgPdQ_R RcTTW',
	    'PoRElE RUIlI RWL`L RbLkL RiPkP RcSkS RXV^V RcVjV RcXlX RZCZG R[J[Q R^M^P R`G`I RcRc[ RdCdG RfJfQ RiMiP RcMaP RXMWQ R]YY[ RUIURSZ RWS^R^XVY',
	    'PoRElE RWJgJ RWLgL RSQ]Q RaQkQ RSS]S RaSkS RRUlU RSNSS RWHWL RXNXS RZCZF RSN]N]S R_H_L R_T_[ RaNaS RdCdF RfNfS RWHgHgL RaNkNkS R_UeYlZ R_UXYRZ',
	    'PoRElE RZJlJ RRMZM RVIVW RZCZG RcPcS RdCdG R[LjLjO RcUeXl[ RjVfX R^W^[ R[L\\O R`L`O ReLeO ReSkT RYWRX RbVYX R[Q^PjPeQ R`TZT RdZ^[',
	    'PoRElE RTLjL R_QkQ R_SkS RRT\\T R_VkV R_ZlZ RTHTL RWTW[ RZCZG R`O`[ RdCdG RfOfZ RTHjHjL RZR]U RSOVR R`O\\S RWMTP RZOUT R[V\\Y RTVRZ R[H[L RcHcL RaL_R',
	    'PoRElE RRIlI RbTjT RTV\\V RbVjV RTY\\Y RbYjY RR[l[ RZCZG RTT\\S\\[ R_M_Q R_G_I RbRb[ RcLcP RdCdG R\\J]L RhNlQ RbIaK RVLTP RZQWQ RSL[LZQ RjKcL RhPbQ',
	    'PobEjE RRGlG R[LcL ReMkM R[NcN RaSjS RTJTQ RWIWQ RTE\\D\\G R[I[PbPaS RbCbG R[IcIcP ReIkIkM R_G]I R\\TTX RWLZLWL RfIfQkPkQ RYPRQ R[SST RYV_Yj[ RYVfV`YT[',
	    'PoRElE RTHiH RVL\\L RbLhL RVO[O RbOhO RaRiR RTT\\T RXW\\W R`[i[ RSJSN RTQTT RTVTZ RXTXZ RZCZG RTQ\\Q\\T R_H_O R`V`[ RdCdG RSJkJkN RbP_S RaRfUlV RhScU^V R`WiWiZ R]YYZTZ',
	    'PoRFlF RXNkN RYQ]Q R`SdT RYW]W R`WdW RXZdZ RZCZH R]N]Y R`N`Y RdCdH RlYk[ RhXj[ R\\J]K RaJcL RhIjL RiUd[ R\\JYL RaI_L RWUUXSZ RSIWK RROVP RkPhX R\\SYT R`QdQ RfIfOhY',
	    'PoRElE R]OjO R]RkR RSSZS R_UkU R`XjX R^[l[ RSKST RVHVY RZCZG RSKZKZS RdCdG ReSe[ R`S_V RaH\\J R`IiIdK]M RbKlM RYUZYRY R]S\\Z',
	    'PoRFlF R]KkK R[NlN RTNZN R^TiT RTTZT Rf[i[ RZCZH R`Y`Z RdPdV RdCdG R^I`K RfNjQ RhIfK R]X\\[ RcK]QhQlQ R_O^VjWi[ RcHaN R_OZR RTIZIZ[W[ R^RbR RTITTS[ RdRgRcR`R',
	    'PoRElE RVLhL RaPlP RZCZF R_W_[ RdCdF RSWgVkX RdYk[ RSN^N]RYT RcS[W RaNlNkR RaNaQeR RWT^U RVHWJZIVJ RSNSQ^QYR RYYR[ RVHhHhJdI RfNfQjR RcJ_I_J[I RXNXQTR',
	    'PoRFlF R[JlJ RSOYO ReOjO R\\ObO R]SkS R]UjU R]WjW R]ZlZ RS[X[ RSJYJYO RZCZH R\\L\\O R^R^[ R`H`K R\\LbLbO RdCdG ReQeZ ReLeO RgHgK ReLjLjO R_PZU RSORTXTXZ',
	    'PoRFlF RZJcJ RSPXP R[PbP RePkP R[SbS ReYlY RSYXY RSSS[ RSISO RSSXSX[ RSIXIXO RZCZH R_J^L R[LbLbW RdCdH ReSe[ ReIeP ReIkIkP ReSlSl[ R[L[WaWc[ R]X[[',
	    'PoRFlF RSJ^J RaMjM RaOjO RTO]O RaRlR RUV\\V Rg[j[ RSRS[ RTLTO RXVXZ RZCZH RTL]L]O R`W`Z RdWdZ RdCdH RaJjJjO RhViX R[TXV RfWfY RSR^R^[[[ RWTWV RaJaTkTj[',
	    'PoRElE R[IcI R\\KbK R\\NbN R\\SbS RRUlU RVQVT RZCZG R\\P\\S R_T_[ R\\PbPbS RdCdG RePeT RhPhS RhGeJ RjIeO RWGSK RYISO RYPYS RkPkR RSQST R_UfYlZ R_UXYRZ RXJSK RYLYOSN RjJdK RjLkNdO',
	    'PoRElE ReLhL R]LaL RVThT Ra[e[ RSMSP RVQVU RZCZF R[H[M R[HbHbM RVQdRhRhU ReHiHiM RSMkMkP RaOaR RWXfXe[ R\\O\\QXS RaUkX RXLUL R]I`J]J R`O\\O RdOaO R]URX ReOhO R^OaO RYO]O ReJiJeJ RXOVO RgOdO R]ZV[ RYGUHVLYK',
	    'PoRElE RbJkJ RaSiS RTU\\U RaWjW RTW\\W RaYjY RTY\\Y RSJSO RTUTY RXHXO RZCZF RSJ^J^O RaPa[ RdCdF RTPiPiS RaUjUjY RcH_K R[L[N RbIdLkO RULUN R\\H[J RiIfM`O RTPTS]R][kZl[',
	    'Po_FjF R_ZjZ R_C_J R`L`Z RjWjZ RUJlJjM RUJUSSZ R_RXS RcR\\R RfQ_R RhQbR',
	    'Po_FjF RYNgL R_PkP Rc[k[ R_C_P RcSc[ RlWlZ RUIkIkL RUIUSSZ R[S[WYZU[',
	    'Po_FjF R_LfL R]McM RXM_M Rd[h[ R`C_PkOkP RUIlIkL R`R_W]YX[ RWTjTi[ RUIUSSZ',
	    'Po`FjF RXMgL R_PlP RZSkS RZVjV RZZkZ RZSZ[ R`C`O RUIlIkL RUIUSSZ',
	    'Po_FiF RaMgM R_MeM RXN_N R_C_QkPjQ RhUlY R_ScU RUJkJjL RYUWY R]U][gYg[ RUIUSSZ RaM[N',
	    'PoaHkH R]OdO Re[l[ RbEbJ ReTeZ RlXl[ RZKkKkM RZJYSV[ RbLaQkPkR R_T]YY[ RUFURTY RZMXW R[FUF RiCZF RgNaN',
	    'PodFjF RdPjP Rg[l[ RdJdP RdCdI RgSgZ R^IkIkL RlWlZ RRIZI[TXU R`Z^[ RbSaY RVCVOTW R^I^S\\Z ReM_M RhLbM',
	    'Po_FiF R_LeL RZMaM RXM_M R_OkO RVSlS R_C_O RaQaS RUIkIjK R[S_WcYj[ RgScW_YW[ RbYl[ RUIUSSZ',
	    'Po_EiE R]LfL RZLcL RXLaL R_OkO RhXkX R`[l[ R_C_N RbQhQhX RkUkX RYQVU RTIkHiL RYR_R]VZYU[ RbQaW RYSZW^Zf[ RTHTSSZ',
	    'Po_FjF R^LeL RZMaM RWM]M R^OjO RVVlV R\\[`[ R_C_N RXR\\R`Qa[ R[S\\U RgSeU RUIkIjL RUIUSSZ RgKaL RgP^Q',
	    'Po_FjF R^LeL RYM`M R^OkO RYRaR RYUjU RU[l[ RYRYZ R_C_O RdUdZ RUIkIjK RUIUSSZ RgKaL RgQ_R',
	    'Po_FiF R^KgK R\\LdL RXLaL R_NkN RZThT RZXhX RU[l[ RZQZ[ R_C_N RZQhQh[ RUHjHiK RUHUSSZ',
	    'Po_FiF R`LgL R^LeL R\\MbM RYM_M R^OjO R][g[ R^R^[ R_C_O RgXf[ RhTlW RYTWX R^QbR RUIkIjK RgQbVW[ RUIUSSZ',
	    'Po`FjF RXMgL R_PlP RV[l[ R^R^Z R`C`O RcRcZ RiSgW RYSZX RUIlIkL RUIUSSZ',
	    'Po_FjF R_MfM R\\McM RXM_M R_PlP RYW^W RdWiW RV[l[ RYRYW R^R^Z R`C`O RdRdZ RUIlIkL RiSiW RUIUSSZ',
	    'Po_EjE R]KgK R[KeK RYKbK R_MlM RYRjR RYTjT Re[i[ RYOYT R`C`M RYOjOjT RUHkHjJ RWWkWi[ R^YW[ RUHUSSZ RaP`W',
	    'PoYE_E RYP^P R\\[k[ RYJYP RYCYI R\\S\\Z ReSaW RkXl[ RhLdU RTI_I_K ReIaR RXSVZ RcCaL RiHfS RiWfW RbHkHkQjW RTITSSZ R^LVM',
	    'Po_EjE R\\KgK RZKeK RXKaLkM R]RiR RVWlW R]O]R R_C_L RYPYThThW R]OiOiR RUHkHjJ RdYl[ R^YV[ RUHTSSZ',
	    'PodFjF RSKZK RRN\\N RdPjP RgZlZ RU[Y[ RSDSJ RSDZD[J RdCdI RdJdP RgSgZ R^IlIkM RlWlZ R`Z^[ RbSaY RUNTSZRY[ R^I^S\\Z RhMdM`M',
	    'PodFjF RRH]H RdQjQ Rg[l[ RXCXG RdCdI ReJeP RhShZ R_IlIkL RlXl[ R[J\\M RUP[V RVWR[ RUJSO RZOUY RcSaZ R_I_R]Y RgMaM',
	    'Po_EjE R]KgK RZKdK RXKaK R_NkN RYT^T RcTiT RVWlW RYPYT R^O^W R`C`N RcOcW RiPiT RUHkHjK RdXl[ R^YV[ RUHTSSZ',
	    'PoeFkF RdMjM RaMgM RRP^P RePkP Rh[l[ RW[Z[ RWGXL RZMZ[ ReJeP ReCeI RhSh[ R_IlIkL RlXl[ RUSWW R[F]K RTESL R\\DTE RcSbZ R_I_U^Z',
	    'PoTFXF ReFkF RRI]I Rh[l[ RSNSY RVKVX ReCeI RhSh[ ReJeQkPkQ R_IlIkL R\\D^G RlXl[ RWPYPVP RZWRY RcSbZ R[D[Q[X R_I_R^Y RgMaM',
	    'PoXF^F ReFlF RYP]P ReQkQ Rh[l[ RXCXI RYJYP R[S[Y RTI^I^K RfJfP ReCeI RiSi[ RaIlIkK RlXl[ R^WZY RWSVZ RdSbZ R\\LVM RTITSRZ RaIaR_Y RjLbM',
	    'PoXF^F RdGjG RbHhH RYP]P RXCXI RYJYP RWS[S[Y RTI^I^K ReWi[ ReJiN RfPkP RfDjE RjI`N R_W[Y RjThW_[ RcPeX RcCeL RkKkNiN RlXk[i[ RWSVY RTITSRZ ReG`H R\\MUM RjRaS',
	    'PodFkF RVG\\G RSHYH R[W[Z RdCdI R_IlIkL RWJZM RXD[E RXOZP RXY[Z R\\K\\N R[TXXRZ RcShShZlYl[ R[ISM RUOWX RUDWK RcSbZ ReKePjPjQ R_I_S^Z RgMaM RZRRS',
	    'PobElE RZE_E RbKlK RWK^K RZN_N RWR`R RWT_T RWW`W RWZaZ Re[i[ RXPX[ RZCZH RZIZN R\\P\\Y RTHaHaK RXOUT RdQkPj[ RTHTTS[ ReEcR',
	    'Po[EaE RZNaN R]RbR RYPY[ R[C[N R_P_[ R_SbV RZT[V RjEeI RkKfP RVRZRVY RTIbHaJ R_R\\Y RlSgXcZ RTHTRS[ R_KWL',
	    'PoeFkF RdMjM RaMgM RTN\\N RePkP RTR\\R Rh[l[ RTKTR RXCXK RTK\\K\\R ReJeP ReCeI RhSh[ R_IlIkL RlXl[ R[T]X R[E]I RTESJ RTTSY RXRX[U[ RcSbZ R_I_U^Z',
	    'PoXF[F RWP[P RbRkR RcTkT RbWlW RXJXP RXCXI RTI\\I\\L R_H_[ RaGaV RbMbR ReYe[ RgMgW RbMkMkR RjYl[ RWSZS[YYY RbY`[ RhXh[ RdFgJlL RdCbH RdFjFgJbL R_D\\N RZMUM RTITTRZ RVSVZ',
	    'PoUQjQ RWYkY RTYhY RUHUR R_C_Y RUHjHjR RfTl[',
	    'PoTQ_Q Rd[k[ RTITS RTI_I_Q RdDd[ R]T`Z RlUk[ RYDZXRY R^XUY',
	    'PoSQ\\Q Re[j[ RSHSR RXCXX RSH\\H\\Q R[T^Y RcR`Z ReFdNaX R_FlFlSj[ RdIbV R\\XRY',
	    'PoSQ\\Q Rh[l[ RSHSR RXCXX RSH\\H\\Q R`DhDiZ RlVl[ RZT\\YRY R`D`S_Y',
	    'PoSQ^Q RSHSR RYCYX RSH^H^Q RcEcU RlCl[ RlSaU R[XRY R]T_YUY',
	    'Po_FlF RSQ]Q Ra[g[ RSHSS RXCXX RSH]H]Q RgFg[ R\\T^Z R]WRY',
	    'PoTSbS RXYcY RUY`Y RSY^Y RTMTS RSI[I\\Y RTMbMbS RlVl[ R`UdY RSEgEgRiZ RaHYI',
	    'PoSQ]Q RSHSR RXCXX RSH]H]Q RdCd[ RfLlQ R\\T^YRY',
	    'PoSQ[Q Re[j[ RSHSS RWCWX RSH[H[Q RaNfS RbC^M R[T\\Z R\\WRY R`IlIlSj[',
	    'PoSQ\\Q Rd[k[ RSISS RXDXY RSI\\I\\Q RdFd[ RkXk[ R[U]Z RiE^G RlN^P R\\XRZ',
	    'PoSQ[Q Ra[k[ RSISS RWCWY RSI\\I\\Q RaFa[ ReCeU RlWk[ RjI]M RkIjTgT R[T\\YRY',
	    'Po_EkE R^NlN RTQ\\Q RTHTS RXDXX RTH\\H\\Q ReEe[a[ R[T]YRY',
	    'Po`EkE R_NlN RSQ]Q RSISR RXCXX RSI]I]Q ReEe[ R\\T^XRY',
	    'Po_OlO RSQ\\Q Ra[e[ RSHSR RWDWX RSH\\H\\Q ReKe[ R`FkFeK R[T\\XRY',
	    'Po_FlF RSR\\R R^[l[ RSISS RXDXY RSI\\I\\R ReFe[ R[U]YRZ',
	    'Po_NjN RbUkU R^UgU R_H_N RdDdT R_HjHkN RSF]F\\ZkYkZ RiQkV RVFVRSZ',
	    'Po`IlI RSQ\\Q R`[l[ RSHSS RXCXY RSH\\H\\Q RaIa[ ReCeI R\\U]YRZ',
	    'PoaHlH RSQ[Q R`[l[ RSHSS RWDWY RSH[H[Q RlWk[ R`MjMaV_Z RbD^M R[T\\YRY',
	    'PoVJhJ RUTiT RUNUU RVDVJ R_J_Y RVDhDhJ RUNiNiT RSZ_ZiYl[ ReWjY',
	    'Po]ElE RTQ\\Q RTHTS RXCXY RTH\\H\\Q RdEd[ RdLhMjP R[T\\YRY',
	    'PoRElE RbMhM RUUiU RUPUU R_M_Y RZIiIhM RUPiPiU RRZgXkZ R\\EZJXLSN',
	    'Po_RjR RWMWZ R_I_R ReCeY R_IjIjR RiTl[ RUH]H[M R\\XU[ RVDSM RhY]Z RjX_Z',
	    'PoSQ\\Q RSHSR RXCXX RSH\\H\\Q RdTjZ RfS_Z R`LeV RdDeI RiKdV R_FcS RkEgS R[T]XRY',
	    'Po^EiE RSQ[Q R]UhU Re[j[ RSHSS RWCWX RSH[H[Q RZT\\YRY R`I_PlPj[ RiFhP',
	    'PoVH_H R\\KdK RWK_K RUViV RRZkZ RUQUU R_O_Z RUQiQiU RZEUG R\\CYF ReWl[ RcEUK RhFfI_MSP RfI^J',
	    'Po^HlH RSQ\\Q Rd[i[ RSHSR RXCXX RSH\\H\\Q R`W\\[ ReDeG RbHbOjNjUi[ RbKaW R[T\\XRY',
	    'Po_ElE RSQZQ RgSjS Rc[k[ RSHSS RVCVX RSHZHZQ RdJd[ RcJkJkS RlWk[ RZTZYRY R_E_S^Z',
	    'Po^PkP RSQ\\Q R_[k[ RSHSS RWCWY RSH\\H\\Q R_E_[ RdEdP R^EkEkR RlWk[ RZT\\YRY',
	    'Po`NjN RSP\\P RSHSQ RXCXW RSH\\H\\P R`EjEjN RhWl[ ReQiY ReNhW R[R\\WRW R`E`Q_X',
	    'Po]EcE ReElE RdOlO RSQ[Q RSHSR RWCWX RSH[H[Q RhEh[ RcN\\P R`E`Q_X RZT[YRY',
	    'PoRFlF RUUiU RSZjZ RUPUU R_C_F R_N_Z RUPiPiU ReWjZ RYH`KiN RfFdH^KTN R_KlN R`KRN',
	    'Po]HlH RSQ[Q RSHSS RWCWX RSH[H[Q RdCdH RRY[X R[V\\Y R_HdUiZm[ RiHeU`Z][',
	    'PoTQ\\Q Rh[l[ RTHTS RXDXY RTH\\H\\Q R_J_[ R_JlJl[ RiTkT RaDfDeNbT ReJhR ReHdQ R[T]YRY',
	    'Po^HlH R_NkN RSQ\\Q R]TlT RSHSR RXCXX RSH\\H\\Q ReCe[ R[T]YRY',
	    'Po_McM RSQ[Q Rf[l[ RSISS RWCWX RSI[I[Q R_D_Z RfDf[ RlXk[ RlKfN RcY\\[ RYT[XRY',
	    'Po`OlO RSQ]Q R`YlY RSHSS RXCXX RSH]H]Q R`F`[ R`FlFl[ R[T^Z R\\XRY',
	    'PoaKlK RSQ]Q RaRlR Rh[l[ RSHSR RXCXX RSH]H]Q RaElElZ RaEaR`X R[T]XRY',
	    'PoSQ\\Q Rd[j[ RSHSR RXCXX RSH\\H\\Q RcLfP R[T]Y RcC_L RiQ_U RaHlHlSj[ R\\XRY',
	    'Po]HlH RSQ[Q RSHSR RWCWX RSH[H[Q RdCdN R_OaTfYl[ R]NjNhScX\\[ R[T\\XRY',
	    'Po\\OlO RSQZQ Rf[i[ RSHSS RWCWX RSHZH[Q R_DiDi[ RcHeL RZT[Z RZWRY R_D_S^Y',
	    'Po`KfK RSQ\\Q Ra[e[ RSHSR RXCXX RSH\\H\\Q R_EfEfL RlDl[ R_K_RgQe[ R[T\\XRY',
	    'Po_NlN RSQ\\Q RSHSR RXCXX RSH\\H\\Q R_F_[ ReY^Z RfEfOhY R[T\\XRY RjD_F RlWk[iZ',
	    'PoSEkE RTIjI RVUiU RXZjZ RUZhZ RVOVT R_M_Z RVOiOiT RdWkZ RaIcKgMlN R_E]JZMRO',
	    'Po_FkF R^NlN RTQ\\Q Rg[l[ RTHTR RXCXX RTH\\H\\Q RhNhZ RlWl[ R_Z][ RbNaX R[T\\XRY',
	    'Po]PlP RSQ[Q RSHSR RWCWX RSH[H[Q R^HjHjP RfVk[ RbU\\[ RdDcQhX RcMbU RZT\\XRY',
	    'Po^KlK RSQ\\Q Re[l[ RSISS RWCWX RSI\\I\\Q RdCeNe[ RlWl[ RgDkH RaV\\[ RcP_Y RdIbT R[T]XRY',
	    'PoSQ[Q RSHSS RWCWX RSH[H[Q RcKfN RdDlL RcF]L R^QjQd[ RdCbH RZT[YRY',
	    'Po`MlM RTQ\\Q RTHTR RXCXX RTH\\H\\Q RgMg[ R[S\\XRX RjD_F_R^Y',
	    'PoSQ\\Q R_RlR RSHSR RXCXX RSH\\H\\Q R_I_T RfCf[ R_IlIlT R[T]Z R\\XRY',
	    'PoaElE R^OlO RSP\\P Rc[h[ RSHSR RWCWW RSH\\H\\P RaEaO RiEi[ RfQ_Y R[S\\WRX',
	    'PoSQ[Q Rd[k[ RSHSS RWCWX RSH[H[Q RdFd[ RlWk[ RiD]F RZT[YRY RkK^M RlQ]S',
	    'Po^IkI RSQ\\Q RSHSS RXCXX RSH\\H\\Q RdDd[ RdIkUlV RdI_V RdGbQ R[T\\YRY',
	    'Po_FlF R`PkP RSR\\R R_[l[ RSISS RXCXZ RSI\\I\\R ReFe[ R[U]YR[',
	    'PoaOfO RSZjZ R_P_Z RVRiRiV RVRVVhWk[ RcCgHlJ R^J[MVO R[DWHRJ RXIgIfO',
	    'PoSQ[Q Rd[h[ RSHSR RWCWX RSH\\H\\Q RaW][ RbEhElN RaF]N RcOaX R_OjOjUh[ R[T\\YRY',
	    'Po^HkH RSQ[Q Rh[l[ RSHSS RWCWX RSH[H[Q R`LhLi[ RcCdF RlWl[ R`L`T_Y RZT\\YRY',
	    'PoTQ\\Q R`ZkZ R^ZfZ RTHTS RXDXY RTH\\H\\Q RhTk[ RbEhEkN RaG^N RdN`Z R[T\\YRY',
	    'PoVUhU RSZjZ RVPVU R_N_Y RVPhPhU RWHTK RdWkZ R]FaH RYE[IbLkN RTEhEdI^LSO',
	    'PoRQ[Q RRHRS RWCWX RRH[H[Q RcSjZ RfS_Z R`LeV RfDiFkL RiLdV RbD_J RZT[XRX',
	    'Po]LlL RTP\\P R]SlS Rf[i[ RTHTR RXCXX RTH\\H\\P R`E`[ R`EiEi[ R[T\\XRX',
	    'PoSQ[Q RSISR RWCWX RSI[I[Q RhMh[ RfFlL RbG]L ReCgH RcCaI RaMaT`Y RZT\\XRY',
	    'Po_IjI R^PkP RSQ[Q RSHSR RWCWX RSH[H[Q RfUl[ RcV][ ReCeQhX RePaX ReNeT RZT\\XRY ReLcU',
	    'PoUIiI RRLlL RUUiU RRZjZ RUDUI RUPUU R_N_Y RUPiPiU RiDiI RdWkZ R_C_I[KTL',
	    'PoSQ\\Q RSHSR RWCWX RSH\\H\\Q RhCh[ R`FdG R_McN RkS_U R[T]YRY',
	    'PoSR[R R_RfR R`[k[ RSISS RWDWY RSI[I[Q R`L`[ R_LfLfR RlWk[ RaC]M RiVfV R_HkHkPiV RZU\\YRZ',
	    'PoSQ\\Q Rf[k[ RSHSS RXCXX RSH\\H\\Q R`E`R ReIfSf[ R`EjEjR RcV^[ RdRbY RlVk[ R[T\\YRY ReMdV',
	    'Po_JkJ RSQ[Q Rb[k[ RSHSR RWCWX RSH[H[Q RcLcZ R_EkEkJ RlWk[ RjObS R_E_Q]Y R[T\\XRX',
	    'PoSQZQ RSHSS RWDWX RSHZHZQ R`H`[ RgEjH RaC]O RlVk[iZ ReCeNgY RgLaM RkLdM RZT[YRX',
	    'Po_NkN RTQ\\Q R\\[l[ RTHTS RXDXX RTH\\H\\Q R_F_[ RgNg[ RjD_F R[T\\YRY',
	    'Po_QlQ RSQ[Q R_ZlZ RSHSS RWCWX RSH[H[Q R_I_[ RfCfZ R_IlIl[ R[T[Z R[WRY',
	    'Po`HlH RcMlM RSQ\\Q RcTlT RSISR RXCXX RSI\\I\\Q RcHc[ R[T]Y RaC^L R\\WRY',
	    'Po]EkE R\\QlQ RSQZQ RSHSS RVCVX RSHZHZQ RdEd[ R_H`N RiHhN RYTZYRY',
	    'Po_MlM RSP\\P R^[g[ RSHSR RXDXX RSH\\H\\P R_F_W RlWl[ ReV]W RjD_E RfDfMhX R[T\\XRY',
	    'PoSPZP RSHSR RWCWX RSHZHZP R\\K`O RfSb[ RbK]W R`D^L Rj[g[ RZT[XRX RbRdV\\V RcIlIlTj[ RgCgMeW',
	    'Po]ElE RSQ[Q R_TeT Rc[i[ RSHSR RWCWX RSH[H[Q R_J_U R_JeJeT RiEi[ RZT]Y R[XRY',
	    'Po]IlI R`QiQ RSQ[Q R`YiY RSHSR RWCWX RSH[H[Q R`C`[ RiCi[ RZT]Y R[XRY',
	    'Po]ElE RSR[R R`TjT R][l[ RSISS RWCWY RSI\\I\\Q R`E`[ R`LjLjT RZT\\YRY',
	    'Po_LlL RSP\\P RSHSR RXDXX RSH\\H\\P RgLg[ RbPlU R[S\\XRX RjD_F_R^Y',
	    'PoaJlJ RSQ[Q RSHSR RWCWX RSH[H[Q R`G`[ RcNeT RaC\\N RiCi[e[ RYT[XRY',
	    'Po_MjM RSQ[Q R\\SlS Rg[j[ RSHSS RWCWX RSH[H[Q R_G_[ RdCdS R_GjGj[ RZT\\Z R[WRY',
	    'PoRF_F RbNkN RVUhU RSZjZ RVQVU RYCYN R_N_Z RcCcM RVQhQhU RXF\\J RlJkN RYFRM RdWk[',
	    'PoSQ[Q R`UgU Rf[j[ RSHSR RWCWX RSH[H[Q R`M`V R`MgMgT RbC]M RZT\\XRY R`HlHlTj[',
	    'Po^ElE RSR[R R][l[ RSIST RWDWY RSI[I[R ReIeY RfKlQ RdJ]Q RfFcL R[U\\YRZ',
	    'PoRQ[Q RRHRS RWCWX RRH[H[Q ReCeN R`NbTgYl[ R^HlHjL R^NjNhTcY^[ RZTZXRY R^H^R\\Z',
	    'Po^HlH RTQ\\Q RbZkZ R`ZhZ RTHTR RXCXY RTH\\H\\Q R_NdSbV_Z RfPdT RiVk[ RhLfR ReCeGaN R[T\\YRY',
	    'Po_IlI R_RkR RSR\\R R^[l[ RSISS RWCWY RSI\\I\\R ReCe[ R[T]YRY',
	    'PogNkN RTQ\\Q R`ZlZ RTHTS RXCXY RTH\\H\\Q R`Q`[ R`QlQl[ RcJ_O RdEcL R_ElEkN R[T]YRY',
	    'Po]JlJ RSQZQ RSHSS RWCWX RSH[H[Q RfDiG RaC`OcUgYl[ R_S[[ R`NiNfUbY^[ RZTZXRY R`J^W',
	    'Po^ElE RSQ[Q Rh[l[ RSISR RWCWX RSI[I[Q R_I_[ R_IlIl[ ReEeMiQ RbS`S ReHdQ RZT\\XRY',
	    'Po^JlJ RSQ\\Q R_ZkZ RSHSR RWCWX RSH\\H\\Q R_Q_[ ReCeQ R_QkQk[ R[T\\YRY',
	    'PodJlJ RTQ\\Q R_ZjZ RTHTS RXCXY RTH\\H\\Q R_Q_[ RdCdQ R_QjQk[ R[T\\YRY',
	    'Po_LiL RSQ[Q R_SiS R][l[ RSHSS RWDWY RSH[H[Q R_E_[ R_EiEi[ R[U\\YRZ',
	    'PoSQ\\Q Ra[k[ RSISS RXDXY RSI\\I\\Q R^H^L RaLa[ ReCeG R^HlHlL RlWk[ RjN`S R[T\\YRZ',
	    'Po_IkI R]PlP RSQ[Q RSHSS RWCWX RSH[H[Q RfVk[ RcU]Z RdCdQhY RdPaW R_E]M RZS[WRY RdLcU',
	    'Po`LiL RSQ[Q RfYjY RSHSS RWDWY RSH[H[Q RcPc[ R]PjPjX ReElL ReCfG RbF]L RcCbH R[T\\YRY',
	    'PoVKhK RXPiP RTPeP RR[l[ RUTU[ RVGVK R\\T\\[ R_C_P RbTb[ RVGhGhK RUTiTi[ RgMkQ',
	    'Po_IgI R^MlM RVUiU RSZeZ RVQVU R`E`L R_N_Y RVQiQiU RhYl[ ReWiY RXIRN RTEkEiH R[LdM RhYcY',
	    'PoSQ\\Q RSHSR RWCWW RSH\\H\\Q RZS[WRY RhDhKjZ R^F^R\\[ RcEcRaZ RjD^E RfShY`Y',
	    'PoUSiS RUMUS R_J_Z RUMiMiS RRHlHlL RTDWG RfVl[ RiDeH R]C_G RRHRL RTZiY',
	    'Po^ElE R^JlJ RSP[P Re[i[ RSHSR RWCWW RSH[H[P R[S[WRX RdPkPjVi[ R^E^R]Y RdJdPbX',
	    'Po]EjE RSR[R R^TkT R\\[l[ RSISS RWDWY RSI[I[R RdPd[ R_EaIeMlO RjFdL]O RZU[YRZ',
	    'Po_QlQ RTR[R R_YlY RTHTT RXCXX RTH[H[R R_I_[ RcCcY RhChY R_IlIl[ R[T\\XRY',
	    'Po]JkJ R_PkP RSQ\\Q R]TlT RSISR RWCWX RSI\\I\\Q ReLe[ RgFlK RcC`J RaL^R R[T\\XRY',
	    'PoSQ[Q R`ZjZ RSHSS RWDWY RSH[H[Q R`Q`[ R^RjRj[ RaGbKgPlR RbC_J RaGiGgKbP]R R[T\\YRY',
	    'PoTF_F RRJlJ RZCZJ RZLZX RTO_O_S RfDjH RgS_[ RgYj[ RiNfU RlVk[ RdCcKeW RTOTT_T`XTY',
	    'PoTQ\\Q RcThT R_YlY RTHTR RXCXX RTH\\H\\Q R_E_[ RcJcT RcJhJhT R_ElEl[ R[S\\YRY',
	    'Po]GlG RaPjP RSQ[Q RaUjU Rg[j[ RSISR RWCWX RSI\\I\\Q RaJa[ R`LjLj[ RcF^O RcCaN R[T\\XRY',
	    'Po]JkJ RSQ[Q RdZkZ RSHSS RWCWX RSH[H[Q RdJdZ RfEiG RlVlZ RiLdN RiPdS RjT`Y RZT[XRY RaCaL_W',
	    'PoUIhI RUOiO RUViV RRZkZ RUFUO R_P_Z RUFhFgI RURiRiV RULiLiO RfXkZ R^D]F RURUV',
	    'Po^IlI R^OkO RSP[P R]TlT RSHSR RWCWW RSH[H[P ReIe[ R`DbG RiCgI R[S[WRX',
	    'Po_HkH R]NlN RSR[R R^TkT R][l[ RSISS RWCWY RSI[I[Q ReCeN RePe[ RZU\\YRZ',
	    'Po\\EfE RSP[P Rh[l[ RSHSR RWCWX RSH[H[P RgGgU RlCl[ R^NbS RaV\\[ RcP_Y R`E]Q R_KdKaV RZT[XRX',
	    'Po`IjI R^PkP RSQ[Q RSHSR RWCWX RSH[H[Q RdCd[ RhUlY RbT\\Y RdNhU RcPaV RaE^M RZT\\WRX',
	    'Po`KlK RSQZQ RaRjR RSISR RWCWX RSIZIZQ R_H_[ RbEjEjS R^J\\P R`C]N RYTZXRY ReEeRdX',
	    'Po_JjJ R_OjO RSQ\\Q RSHSS RWCWX RSH\\H\\Q R_E_[ R_EjEjO ReUjZ RcOfW RkReU R[S\\XRY ReY]Z',
	    'Po^LlL RSQ[Q R_ZjZ RSHSS RWCWX RSH[H[Q R_R_[ ReEeR R_RjRj[ RZT[Z R[WRY RjD^F',
	    'Po]HlH RSQ[Q RSHSR RWDWX RSH[H[Q RdCdG RgJlN R`K]O RaOcUgYl[ RhOeUaY\\[ R[T\\XRX',
	    'PoeMjM RSQ[Q RSHSR RWCWX RSH[H[Q R\\QcQcZ ReQlQlZ RbI^M Rk[h[ RcCbJ Rb[_[ R]FkFjM RZT[XRX R_O^Y RhOgX',
	    'PoSQ[Q R_[c[ Rh[l[ RSHSS RWCWX RSH[H[Q R]EcEc[ RfElEl[ RjQfT RlOhS R^I`N RfIhN RcO`S^T RZT[Z R[WRY',
	    'Po_IlI R^QlQ RSQ[Q RSHSS RWCWX RSH[H[Q RhIh[ RiCfI RaDbH RbIbQaW R[T\\YRY',
	    'Po]HlH R^NkN RTQ\\Q R_ZjZ RTHTS RXCXY RTH\\H\\Q R_R_[ RdCdN R_RjRj[ R[T\\YRY',
	    'Po`LhL RSQ[Q R_ZjZ RSHSS RWDWY RSH\\H\\Q R_Q_[ R_QjQj[ RfGlL ReCgH RbG]L RcCaI R[T\\YRY',
	    'PoSQ[Q RSHSS RWDWY RSH[H[Q R`HdJ ReC_I RbGkGeM`SfU RgK^O RbR]T RcQlQjTgX_[ R[T\\YRY',
	    'Po\\FlF R^OjO RSQ[Q RSISR RWCWX RSI[I[Q R]JjJkN ReUjZ RdCdSgX RdRbV_Y[[ R^N]SkRkWhV RYT[XRX RdNcU',
	    'PoSQ[Q Rh[l[ RSISR RWCWX RSI[I[Q R^E^[ R^ElEl[ R_MbOcS RfMiOfV RfJjJgT R`JcJaT RZT\\XRX',
	    'Po^ElE RSQ\\Q R_RkR R_YkY RSHSR RXCXX RSH\\H\\Q R_K_[ R_KkKk[ ReEcK R[T]YRY',
	    'PoSE\\E RhNlN RVUiU RSZjZ RVQVU RXEXL R_N_Z R_D_K R_DhDiN RVQiQiU RlKlN ReWl[ RaHeI R]M[N R\\KRM',
	    'PoRF]F RSI\\I RRM^M RfNjN RUUiU RUQUU RXDXN R_O_Z RUQiQiU RcJ_N RRZhYkZ RdEbK R_EkEjN',
	    'Po]KlK RSQ[Q RfUjU Rb[k[ RSHSS RWCWX RSH[H[Q RcNcZ RbNjNjU RlWl[ R`FhFeJ RaC^J^T\\[ RZT[XRY',
	    'PoTFjF RSKlK RWUgU RTZjZ RWOWU R_N_Z RVPhPhU RhXk[ RdCdLgOlQ R[C[LWPRQ',
	    'Po]ElE R^MkM RSQ\\Q R^TkT R\\[l[ RTHTS RXDXY RSH\\H\\Q RdOd[ RgHkM RcE_M R[T\\YRY',
	    'PoRFlF R^McM RUUiU RRZjZ RUPUU R_C_F R_M_Y RcFcM RUPiPiU RfHiIkL RVHSK ReWjZ R\\F[KYMUO',
	    'Po]GhG R\\MlM RSQ[Q Ra[k[ RSISS RWDWY RSI[I[Q RaPa[ RbCbM RkXk[ RjGaQ\\S RiR`U RZT\\YRY',
	    'PoRF\\F RUVhV RRZjZ RUQUU RXCXO R_N_Z RTOVPhQhU R^FhFiM RiOlNlO RbK^O R_IeL RdWk[ RcCaL R\\IRK',
	    'Po^GkG R]QkQ RSQ[Q RSHSR RWCWX RSH[H[Q RfVk[ RcU][ R_J`N ReCdQgX RdQaX RiJgN RZT[XRY RdLcU',
	    'PodGjG RaGhG R_GeG RSQ[Q R\\RlR Rf[l[ RSHSR RWCWX RSH[H[Q RgRg[ RlXl[ R`W][ RkLkO R]HbGcLfNjO RhJ_N RaCcK RaR`Y RZT[YRY',
	    'PocElE RSQZQ RiWkW RSHSS RWCWX RSHZHZQ R]F]Q RdJdW RhEh[ RdJkJkV RZTZYRY RaDaQ_W',
	    'Po]GlG RSQ[Q RSHSR RWCWX RSH[H[Q RhQh[ RdCeG RaIdMkP RiGgJeM^P R_Z\\[ RZT[XRY RaR`Y',
	    'Po^ElE RSR[R R^TkT R\\[l[ RSISS RWDWY RSI[I[R RdTd[ RhLjP R`G^LaQ RcLeQ RjGhL ReGcL RZUZYRY',
	    'Po]EdE R]PlP RSQ[Q Rd[h[ RSHSR RWCWX RSH\\H\\Q RhMh[ R_SbW R^G`L RdGeK RjFiK RjDcD R[T\\YRY',
	    'Po]HkH RSQ[Q Ra[d[ RSHSR RWCWX RSH[H[Q RdCd[ R^LaN RgSlX RgDjF RdQ^X RkLfQ RdLiV RdJgT RZT\\YRY',
	    'Po`OjO RSQ\\Q R`TjT Rf[j[ RSISS RWDWY RSI\\I\\Q R`K`[ ReCeK R`KjKj[ RhElI RaF^I R[T\\YRY',
	    'Po^PlP RSQ[Q R^UlU Rg[l[ RSHSS RWCWX RSH[H[Q R^K^[ ReKe[ R^KlKl[ RaGiK R_EkEiGdI R[T[YRY',
	    'Po\\HlH RSQ[Q RSISR RWCWX RSI[I[Q R^NaQ ReUk[ RhNlQ RbU\\Z RdCdQgX RiKfR R_K\\R RdP`W RZT[XRY RdKbT',
	    'PoSQ[Q R_RiR Rf[l[ RSHSR RWDWX RSH[H[Q R_K_R RfRf[ R_KiKiR RlXl[ R`W\\[ R_H\\K RaEhEjJ RaR_Y R[T\\XRY',
	    'Po]GlG R^NkN RSQ[Q R^SkS R]VlV RSHSR RWCWX RSH[H[Q R^J^R RdCd[ R^JkJkR RZT[XRX',
	    'Po_JjJ R_PjP RSQ\\Q R_UjU RSHSR RWCWX RSH\\H\\Q R_E_U R_EjEjU RhWl[ RaW]Z R[S\\WRX',
	    'Po\\LlL RSPZP R][`[ RSHSR RWCWX RSHZHZP R`E`[ RhDkG RhUb[ RjOfW RcD[F RdQ\\T RfCfNhY RZTZXRX RlWk[i[',
	    'PoSQ[Q R_ZjZ RSHSR RWCWX RSH[H[Q R_S_[ R_SjSj[ RcIeK RdDkK R^NjNeR RdD]K RZT[YRY',
	    'PoTIlI RVTfT R[YfY RTYaY Re[j[ RTDTI RVPVV R\\E\\J R^N^Y RdEdJ RTDlDlI RdVgZ RYJTO RVPfPfU RVMlMlTj[',
	    'Po_HlH RSQ[Q RdRkR Rd[l[ RSHSS RWCWX RSH[H[Q ReLeZ R`WdZ R_TbY R_LkLiO RaC]K RZT\\XRX R_P^Y',
	    'Po^PjP RSQ[Q R_TiT R]WlW RSHSS RWDWX RSH[H[Q RdNd[ RbD]J R`GdLlO RaFiFdK\\O RZT[YRY',
	    'PoUDjD RXGhG RUJkJ RZO`O RVVhV RVRVV R_P_Y RVRhRiV RSZiYk[ RfWjY R`LeNkP RhKcM RUDUITO RZJ[OVO',
	    'Po_EkE RaIkI R_NlN RSQ[Q RSHSR RWCWX RSH[H[Q RbNbZ RhWl[ RkQhT RfRiY ReNhW RhY_Z R_E^R]Z RZT[XRY',
	    'Po^GlG R_PkP RSP[P R_UkU Rh[k[ RSHSR RWCWW RSH[H[P R_L_[ ReCe[ R_LkLk[ RhDkE R[S\\WRX',
	    'Po_IjI R_NjN RSQ\\Q R_SjS Rf[l[ RTHTR RXCXX RSH\\H\\Q R_D_S RgSg[ R_DjDjS RlWl[ R`Y[[ RbS`Y R[T\\XRX',
	    'PoRF]F R`IlI RTOXO RUUiU RSZjZ RUQUU RXCXO R_O_Z R`E`K RgIgO RUQiQiU ReWjY R^N\\N RjD`E R]IRK',
	    'PoaKjK RSQ[Q R\\TlT RSHSR RWDWX RSH[H[Q RaDaK R^F^OiNiT RaDjDjK RdTfWhYl[ RbV`Y[[ RcQbX RZT[YRY',
	    'PoSQ[Q R\\SlS Ra[d[ RSHSS RWDWX RSH[H[Q RdQd[ RcGdJ R_MjMgOdP RjGkK R_E]K RjD_E R[T\\YRY',
	    'Po\\LeL RSP[P Rh[l[ RSHSR RWCWX RSH[H[P RaEa[ RgFgU RlDl[ RaNeR RaK\\U RdD\\F RZT[XRX',
	    'Po_KjK R_PjP RSQ[Q RSISR RWCWX RSI[I[Q R_F_Z RdCdG R_FjFjP ReUk[ RdPgX RkSfU RfY\\Z RZT\\XRX',
	    'Po^HcH RfOlO RSR[R R]UkU R\\[l[ RSISS RWDWY RSI[I[R R^D^O RdPd[ RfDfO RlLlO RlGeI RcN]O R[U[YRZ',
	    'Po`KiK R]PlP RSQ[Q Ra[d[ RSHSR RWDWX RSH[H[Q RdKd[ ReDlK RcCbF]K RiSkY R`S]Y R[T[XRY',
	    'Po`JkJ RTP\\P R_RlR R_VlV Rh[l[ RTHTR RXDXX RTH\\H\\P R_M_[ R`D`J R`DkDkJ R_MlMl[ R[S]XRX',
	    'Po]IlI RSQ[Q Rf[j[ RSHSR RWCWX RSH[H[Q ReDeO RdHkO RaW][ RdH^O RbQ`Y R^QgQfVkUj[ RjC^E RZT[XRY',
	    'Po\\HdH R\\NdN RSPZP Rg[k[ RSHSR RWCWX RSHZHZP RgSc[ R`C`M]W RYTZXRX RaRcW\\W ReJlJlVk[ RhChMfW',
	    'PodHkH RUI[I R^LkL RYNlN RTZjZ R^G^K R_P_Z R]EdEdL RVRiRiV RVRVVhWk[ RSEYDTI RZJVNRO RVM_N RiC`D',
	    'PogMkM RSPZP RcVlV RbZlZ RSHSQ RWCWW RSHZHZP RdKdV RgEgV R]Q_U\\Z R[E_E\\N`M_W RkDbF RYTZXRX',
	    'PobMlM RSQZQ RcUkU Rb[l[ RSISR RWCWX RSIZIZQ RhEhU R^VbZ R^W[[ R]R_Y RjDbF R\\E`E\\N`N_W RYT[XRX',
	    'Po_PgP RSQ[Q R_UgU Re[j[ RSHSR RWCWX RSH[H[Q R_L_W RcLcU R_LgLgU RaC]L R_HlHlTj[ R[T\\XRY',
	    'Po^PjP RSQ[Q Rf[l[ RSHSR RWCWX RSH[H[Q R^I^P RgPg[ R^IjIjP RlWl[ RaW][ R`CbG RiCfI RbPaX R[T\\XRY',
	    'PocLgL RSQ[Q R^ZlZ RSHSS RWCWY RSH[H[Q R^E^[ RcEcZ RgEgZ R^ElEl[ R[U\\YRY RgScS',
	    'Po_JkJ RSQ[Q Rg[l[ RSHSS RWCWX RSH[H[Q R]O][ R_D_J R_DkDkJ R]OlOl[ ReJeQjV RdR`W RZT[YRY ReLdS',
	    'Po]HdH R\\OeO RSQ[Q RfXlX RSHSR RWCWX RSH[H[Q RfFf[ RfFlFl[ RaGaSdW R^C\\K RZT\\XRX RaN_W',
	    'Po[JbJ RSQYQ RbZkZ RSISR RVCVX RSIYIYQ R_C_[ R_NbQ RiSl[ RdFcO ReEiEkN R_F\\S RfMdY RXTYXRX',
	    'PobEkE RTI\\I RbIiI RbLlL RVUiU RSZjZ RVQVU RSE\\D\\K R_O_Z RbCbO RVQiQiU RdWl[ RRL[LUP',
	    'Po_JkJ R^NlN RSQ[Q R`ZkZ RSHSR RWCWX RSH[H[Q R_D_T`[ RfJfT R`TkTk[ R_DkDkJ RZT[XRY R^N\\[',
	    'PoUViV RS[k[ RRFRI RUQUV R_C_F R_O_Z RZIYNeMdN RUQiQiV RRFlFlJ RgIlN RVJSM ReXlZ R[G`H RdI\\MRO',
	    'PoSFjF RXHeH RXKeK RRNlN RSZjZ R_P_Z RWQWUgVk[ RYCYNURhQlS RWQRS ReCeNhQgV',
	    'Po]GlG R`LiL R`PiP RSQ[Q R\\TlT RSHSS RWCWX RSH[H[Q R`C`T RiCiT RgWl[ RbW][ RZT[YRY',
	    'Po]GlG R_LkL RSQ[Q R_RjR R_VjV Rf[j[ RSISR RWCWX RSI[I[Q R_O_[ RdCdG R_OjOj[ RgIlM RbG`L RZT\\XRY',
	    'Po_MkM RSQ[Q R^ZlZ RSHSS RWCWX RSH[H[Q R^E^[ ReHeW R^ElEl[ RhQjU RdNiR RcQ_U ReKaS RZT[Z R[WRY RiG`H',
	    'Po^GlG R]MlM RSQ[Q R`UjU R`ZjZ RSHSR RWCWX RSH[H[Q R`Q`[ RbCbM RhChM R`QjQj[ R[T\\YRY',
	    'Po]JlJ RaPdP RSQ[Q R\\[l[ RSISS RWDWX RSI[I[Q R^R^[ RbRb[ ReHeP RfRf[ R^RkRk[ R_EjEfG RYT[YRY',
	    'Po^HkH R_LjL R]PlP RSQ[Q Rf[l[ RSISR RWCWX RSI[I[Q RdHdP RgPg[ RlWl[ R`CaG R`W\\[ RhCgG RbP_X RZT\\XRX',
	    'PoRG]G R_IlI RUViV RSZjZ RUQUV RXCXO R_O_Z R_E_L RgIgO RUQiQiV RWG\\L RWIRM ReWkZ RXEVK RjDaE',
	    'Po\\JcJ ReLlL RSPZP RSHSR RWCWX RSHZHZP R_C_[ RiLi[ R_NcQ R_I\\U RZT[XRX RkDdFdScZ',
	    'Po`KhK RSQZQ R]TjT Rh[j[ RSHSS RWCWX RSHZHZQ R]O][ RaOa[ RfOf[ R]OjOj[ RdDkL RcD\\L RZT[Z RZWRY',
	    'Po^LkL RSQZQ R^RlR RSHSS RVCVY RSHZHZQ R^GkGkL RfVkZ RhOjQ RdCeG ReRhY RYTZYRY R^G^R]Z RePeTcW`Z^Z',
	    'Po\\FkF R\\MlM RSQ[Q R^QiQ RcUjU Rc[l[ RSISR RWCWX RSI[I[Q RdCdZ R^IiIiQ R^S\\Z RYT[YRY R^VbZi[',
	    'PoaIiI RaMjM RSP[P RbWiW Rg[l[ RSHSR RWCWX RSH[H[P RbQbY ReFeO RbQiQiW R_ElEl[ R_E_S]Z R[S[XRX',
	    'Po_LhL R]PiP RSQZQ R^WhW Re[j[ RSHSS RWCWX RSHZHZQ R^R^V RcLcV RhRhV R_C[L R`I^N RZTZZ RZWRY R^HlHlUk[',
	    'Po^IjI R^MjM RSQ[Q R^SdS Rf[l[ RSHSR RWCWX RSH[H[Q R^D^M R^O^[ RfOf[ R^DjDjM RlXl[ RkReT RdY^Z RZT[XRX',
	    'PoSQ[Q RbWfW R^YlY RSHSS RWCWX RSH[H[Q R^I^Y RfIfW RlIl[ RaLcO RjLgO R_EkEfJ RgRkS RdR_T R[T\\XRY',
	    'PoSQ[Q R_TdT RgTlT R_ZlZ RSHSS RWCWX RSH[H[Q R`O`[ RgOlOl[ RbGkGiK RcC`K RdN_P R[T\\Z R[WRY',
	    'Po\\HlH RSQZQ R]RcR RSHSR RVCVX RSHZHZQ R]K]R R]KcKcR RlWk[ RhCkF RgUa[ RkLfW RZTZXRY RdU\\V RfCfNhY',
	    'Po\\FkF R]JjJ R\\MlM RSQZQ R^RiR R]VjV RSISR RWCWX RSIZIZQ RdOd[ RgMlR R`N\\S RfJhP RbH_P RcC`N RZT[XRX',
	    'PoUG[G R_IkI RTK[K RXO[O RSZjZ RUD\\D\\O R_P_Z ReDeI RURiRiV R_DkDkI R`D`OlNkO RURUVhVk[ RUDTJTO',
	    'Po^KkK R^PkP RSP[P R]UlU RSHSR RWCWW RSH[H[P R^F^O RgQg[ R^FkFkO ReFeP`T RdCdF R[S\\XRX',
	    'Po]IlI RSQ[Q R]RlR RSHSR RWDWX RSH[H[Q RdFdO RdHiLlN RdH_N RcP_WkZ RiShVeY][ RiD_E RZT[XRY',
	    'Po]FlF R_NjN RSQ[Q R]UlU R`[d[ RSHSS RWCWX RSH[H[Q R_I_M RdCeF ReTe[ R_IjIjM R_QjQdS RZT[YRY',
	    'Po_IjI R_MjM RSQ[Q Rg[k[ RSHSS RWCWX RSH[H[Q R_D_M R_DjDjM RaO]S RcT^Y ReXc[ RhQdZ RcQaV R[T\\YRY RaM_RlRk[',
	    'Po]KbK RfKlK R]OlO RSQ[Q RfRlR RSHSS RWCWX RSH[H[Q RbCbK R]SbScZ RfCfK RfRfZ RlDl[ RZTZWRY R]D]S\\[',
	    'Po]GlG RSQZQ R\\TlT RSHSS RVCVX RSHZHZQ RdQd[ R`LcO RgLkP RcCeG RhIeP RaI^P RYTZYRY',
	    'Po_IjI R\\NlN RSQ[Q RdWhW R`[j[ RSHSR RWDWX RSH[H[Q RaRa[ R`RhRhW RhOlS RhDkH RaO\\S RjXj[ R`E]H RfIiQ RcH_Q RdDaO RZT[YRY',
	    'PoRD]D R`EkE RRK]K RRN^N R\\ZjZ RXZgZ RSZcZ RRDRN RXDXG RXKXN RRG]H]K R_O_Y RTQiQiV RTQTVhWl[ RaFfLlN RjFeK_N',
	    'Po]FlF R`IeI RSQ[Q Rg[k[ RSISR RWCWX RSI[I[Q R^M^[ RbCbH RgCgH R^MkMk[ ReQiU RdR`U RdNbT ReKeR RZT\\XRY',
	    'Po_KcK RfKkK R_QkQ RSQ[Q Rf[l[ RSHSR RWCWX RSH[H[Q R_E_P RgQg[ ReEkEkP RlWl[ RaW][ RdD_F RbQ`Y RZS\\XRY',
	    'Po]FkF R^IkI R]LlL RSQ[Q R_TjT Rf[j[ RSHSS RWDWY RSH[H[Q R_O_[ RdOdT RdCdL R_OjOj[ R[T\\YRY',
	    'Po`IlI R`NkN RSQ[Q R`TkT R`YlY RSHSS RWDWX RSH\\H\\Q R`H`[ RfGfY RbC]M RhCfI R[T\\YRY',
	    'Po\\ElE RSQZQ Rh[k[ RSHSR RWCWX RSHZHZQ R]J][ RdEd[ R]JkJk[ RfMhMiU RaMbU RhMfU RaN_V RYT[XRY',
	    'Po^IjI R^NjN RSQZQ R]RlR RSHSS RWCWX RSHZHZQ R^D^N RdDd[ R^DjDkN ReSlY RcS]Y RYTZXRY',
	    'PoSQ[Q RhUkU Rf[l[ RSHSR RWCWX RSH[H[Q R]G]J RdCdG RfLf[ ReLkLkU R]GkGlJ RlXl[ R]QaT RaW\\[ RcQ_Y R_J\\S R_McMaW RZT[YRY',
	    'Po]GkG R^NjN RSQ[Q R^RjR RSISR RWCWW RSI[I[Q R^J^R RdCd[ R^JjJjR ReTlZ RdRgU RcS\\Z RZS[WRX',
	    'PoRDlD RUH\\H RbKiK RROlO RRZkZ R\\D\\H R_P_Z RbKbO RURiRiV RbDbHiHiK RURUVhVl[ RUHUK\\K\\O',
	    'PodKkK R\\KaK RSQYQ RSHSS RVCVX RSHYHZQ R`C`K R\\Q`Q`[ RgWdZ RfQjY RjPfY ReLiW RkKgW RYTYXRY R\\D\\T[[ RkDcEcQbZ',
	    'PoSQ[Q RSHSR RWCWX RSH[H[Q RfVl[ R]SbX R_HbL RgHkM RiHdN R`V\\[ RiUd[ RaI\\N ReRhX R]HaI ReEkEhK RdPkPhX R]EcE`L R\\PbQ_X RZT[XRY',
	    'Po_LjL R`PjP RSQ[Q RaUjU Rh[l[ RSHSS RWCWX RSH[H[Q R^E^[ RaPaU R^ElEl[ RdMeO RbGcJ RhGfL RZT[YRY',
	    'PoRE^E R`EkE RSI]I R`IkI RUUiU RSZjZ RUQUU R_O_Z RUQiQiU RYJ]N RfJjN ReJaN RWKSO ReHgL ReWjZ RfEdL RXEWL',
	    'PohHlH R^HaH R^PkP RSQ[Q R^UlU RSHSR RWCWX RSH[H[Q RaEaL ReCe[ RhEhM RZT[YRY',
	    'Po_JiJ RSQ[Q R^QkQ R^VkV Re[k[ RSHSS RWCWX RSH[H[Q R^M^W R_E_I ReJe[ R_EiEiI R^MkMkV RlXk[ RZT[YRY',
	    'Po_IjI R`OiO RSQZQ R_UjU R]YlY RSHSS RWCWX RSHZHZQ R]D][ RdIdU R]DlDl[ RgQiS RYT[YRY',
	    'Po^MkM RSQ[Q R^RkR R\\VlV RSHSS RWCWX RSH[H[Q R^I^R RdId[ R^IkIkR R^D_G RcCdG RjDhI R[T[YRY',
	    'Po^LkL RSQ[Q ReRkR Rd[l[ RSHSR RWCWX RSH[H[Q R^G^J ReLeZ R^GkGkJ R`TbY RcCeG R`Q^Z R[T[XRX',
	    'Po^OiO RSQ[Q RSISR RWCWX RSI[I[Q RcJc[ R^JjJkXl[ R]D_H RbCcH RjDhI R^J^UjTj[ RZT[XRX R^P]Y',
	    'Po\\LcL RSPZP RSHSQ RWCWX RSHZHZP R`E`[ RjDj[ R`NcR RdMgP ReGhJ R`L[U RcD\\F RlScU RYTZXRX',
	    'Po]KdK R]PdP RSP[P R^ZdZ RSHSR RWCWX RSH[H[P R]G]O R^Q^Z R]GdGdO RdWdZ RgEg[ R`C_G RdS]T RgElElViV RZT[XRX',
	    'Po_HkH R_LkL RSQ[Q R_XiX Rf[k[ RSHSR RWCWX RSH[H[Q R_D_L R_R_Z R_DkDkL RdSgW RaN\\S RdSaW R_PlPlUk[ RZT[YRY',
	    'Po]FlF R_MiM RSQ[Q R^SjS R`[d[ RSHSR RWCWX RSH[H\\Q]S R_I_M RdSd[ R_IiIiM R]PlPlS RcCdF R[T]Y R\\XRY',
	    'PoaHjH RaLjL RSQZQ R_SkS R_WkW R_ZkZ RSHSS RWCWX RSH[H[Q R_D_M R_O_[ R_OkOk[ R_DlDlM RZT[Z R[WRY',
	    'PodHiH RcMlM RSQ[Q R_SjS R_WjW Rf[j[ RSISR RWCWX RSI[I[Q R_O_[ RdDdL R_OjOj[ R\\ElEkG R`G]N RZT[XRY',
	    'Po\\ElE RbPfP RSQZQ RbTfT R]ZkZ RSHSS RWCWX RSHZHZQ R]J][ RbJbZ RgJgZ R]JkJk[ RdEcJ RZT[YRY',
	    'PoSPZP RSHSR RWCWX RSHZHZP R]N][ R_QaR R_TaV R_EdEjJ RdD]K RdTh[ RhTl[ R_KhJlK RfNeT RjNhU R]NbNb[`[ RZTZXRX',
	    'Po^GdG RSQZQ R_VkV R_ZkZ RSHSS RWCWX RSHZH[Q R^C^N R_Q_[ RcOcQ R_QkQk[ RfCfNlLlN RkFfH RZT[Z RdM]M R[WRY',
	    'PoVH[H RUN\\N R_QkQ RUR\\R RUU\\U RUX]X RUKU[ RUK\\K\\Q R_I_Q R_IkIkQ RXC]H RXDRJ RiTl[ ReDfY`Z RjYbZ',
	    'Po_KiK RSRZR R_SjS R_WjW R\\[l[ RSISS RWCWY RSI[I[R R]G]J R_N_W R_NjNjW R]GlGlJ RdCdG R[T[YRZ',
	    'Po^KkK R^OkO RSR\\R R]RlR R^VjV R\\[l[ RSISS RWDWY RSI\\I\\Q R^G^O RdRd[ R^GkGkO RcCbF R[U\\YRZ',
	    'Po]ElE RaJjJ RaMjM R_PlP RSQZQ R]YlY RSHSR RWCWX RSHZH[Q R]E][ RaGaM RaGjGjM RaSkW RdMaT RiPfU_W RZT[XRX',
	    'Po]ElE R^MkM RSP[P R`UkU RSHSR RWCWX RSH[H[P R^H_S`[ RdPd[ RhPh[ R^HkHkM R`PlPl[i[ RZS[XRX R^O\\Z',
	    'PobOeO RSQZQ R\\SlS RSHSS RWCWX RSHZHZQ RdPd[ RfHfN RdRkZ R]IkIiM RdR]Z RaFhI R^DiEdG RdIaL\\N RZTZXRY',
	    'Po]GlG R_KeK RSQZQ RSHSS RWCWX RSHZHZQ RlXl[ RiDkF R`UdV_[ RjPfZ RkLhW RcK`T R_OeOcW RZTZXRY RhChNiZ R]G]T\\[',
	    'Po_NjN RSQ[Q R_RjR R_VjV RSHSS RWDWX RSH[H[Q R_I_V R^JjJjV RhXk[ RaY][ RbE]J RbFhFfJ R[T[XRX',
	    'Po_JhJ R\\LlL RSP[P RSHSR RWDWX RSH[H[P RhUlY RjOgS ReOiW RdLbO]Q RcR]U ReTbW\\Z Re[a[ ReLhU R`C_J R`EhEgL RZSZXRX ReReWeZ',
	    'Po^JlJ RSQZQ RaRlR RaVlV RaZlZ RSHSS RWCWX RSH[H[Q RaMa[ ReDeJ RaMlMl[ R^DlDlJ RZTZXRY R^D^S][',
	    'Po^IlI R^MlM RSQ\\Q R_SkS R_VkV Rg[k[ RSHSS RWDWX RSH\\H\\Q R^E^L R_O_[ ReEeL R_OkOk[ R^ElElL R[T\\YRY',
	    'Po]ElE R_MjM RSQ[Q R^UlU R^ZlZ RSHSR RWCWX RSH[H[Q R^P^[ R_H_M RdPdZ R_HjHjM R^PlPl[ RZT[YRY',
	    'Po\\ElE RSQZQ R\\TlT RSHSR RWCWX RSHZHZQ R^I^P RbIbP RfIfP RdSeWgYl[ RdEbJ RbV`Y\\[ RdQbX RYTZXRX R^IjIjPhP',
	    'Po^ElE R_MjM RSQ[Q R_RjR R_VjV RSHSR RXCXX RSH\\H\\Q R_I_V ReEdI R_IjIjV RgXl[ RbX\\[ R[T\\XRY',
	    'PoeHlH R[HcH RSPZP RdSlS RSHSR RWCWX RSHZHZP R^NbNbZ RfCcK Ra[^[ ReLlLhPh[d[ R_C_H^Q\\Y RZTZXRX',
	    'PobEgE RSQZQ ReRjR R]VjV R]ZjZ RSISR RWCWX RSIZIZQ R]M][ R_IgIfR R]MjMj[ RgFlJ RbIbQ_S R_G\\K RZT[XRX',
	    'PobGlG RUUiU R_P_Y RRZiYk[ RRI_I\\K RbGfLkN RWF\\H RfWiY RcCaI RiHeL_N RXJRM RUQiQiU RUQUU RSE^DZHZNWN',
	    'Po]LbL RdLkL R]PbP RSQZQ R]UbU RSHSR RWCWX RSHZHZQ R]E][ R]EbEcL RdEkEkL RfVl[ RdQhX RdPkPhVeYa[ RYTZYRX',
	    'Po]FlF RSQ[Q R\\SlS RSHSS RWCWX RSH[H[Q R_L_R RaCaI RgCgI R_LiLiR ReUjZ ReIdSgX RcU`Y\\[ RZT[XRY RdNcV',
	    'PoUIdI R`VjV RSV]V RRZ]Z R_ZkZ RSQSV RXOXZ RSQ]Q]V R`Q`U ReNeZ R`QjQkU RlKkN R\\W^[ RiWlZ RgLjN R]IZLSN RTEfEhM R\\E\\K',
	    'Po]IlI R^PkP RSR[R R^SkS R^WkW R\\[l[ RSISS RWDWX RSI[I[R R^L^S R]EeEe[ R^LkLkS RZT[XRY RjDbE',
	    'Po_HjH R_LjL R\\OlO RSP[P Rf[j[ RSHSR RWCWW RSH[H[P R_D_L R_DjDjL R`Q\\U RbU]Z RgRdZ RcRaW R_SkRj[ RZS[WRX',
	    'Po^GhG R\\LlL RSQZQ R`UjU R`ZjZ RSHSS RVCVX RSHZHZQ RaPa[ RcCcL R^QjQj[ RjGbO\\R RZTZYRY',
	    'Po^LjL RSQ[Q RSHSS RWCWX RSH[H[Q R^E^K RkEkK RhNkQ ReGhJ RaM]Q RbP]U RgDbI R`SdXk[ R`EeG R`SiSeX^[ RZT[YRY',
	    'Po_HjH R_LjL R]PlP RSQ[Q RdUkU RcZlZ RSHSS RWCWX RSH[H[Q R_E_L RePeZ R_EjEjL R_U`X R_R]Z RZTZXRY',
	    'Po_GlG R_MjM R_PjP RSQ[Q RSHSR RWCWX RSH[H[Q R_I_P R_IjIjP RcP\\V R`TcXk[ RaC^I R`SiSeWaY\\[ RZT[XRY',
	    'Po^KjK R]OlO RSQ[Q RaSjS RSHSR RWDWX RSH[H[Q RdFdI RiFlI R`SeXl[ R_E]J RaR]Z RiTeX_[ RbK_W RiD_E RZT[XRY',
	    'Po\\GlG R_MiM R]RkR RSRZR R^VjV R\\[l[ RSISS RWDWZ RSIZIZR RdSd[ RdJdR RfGkM RbG\\M RcCaJ RZU[ZRZ',
	    'PoUFjF RULjL RaUjU RSU]U R_ZkZ RSQSU RUFUK RXNXZ RSQ]Q]U R_C_G RaQaU ReNeZ RaQjQjU RRZ]Y^[ RiWl[',
	    'Po\\GeG RSPZP R]QdQ Ri[l[ RSHSR RWCWX RSHZHZP R]K]Q RaCa[ R]KdKdQ RhFhT RlCl[ R`QeV RZSZX RaP^W[XRX',
	    'Po^EkE R^HiH R\\LlL RRQZQ R\\SlS R^XlX RRHRS RVCVX RRHZHZQ RdCdK RcTeV RcOeR RYTZXRY Rh[e[ R_NjNjVh[ R_N_Q^W',
	    'Po\\IdI RfKlK RSQZQ RfRlR R]WcW Ri[l[ RSHSS RWCWX RSHZHZQ R]P]X R`C`O R]PcPcW RfElEl[ RZT[YRY RfEfSeY',
	    'PoRH]H RSZjZ RYDYO R_P_Y RVQiQiV RVQVVhVl[ RXH[K RkFhI RYHRN ReJhMlO R`F_J RdFdKaM]O ReDeK R\\DSE',
	    'Po[GlG RcLhL R^OkO RSQZQ R\\SlS RSHSS RWCWX RSHZHZQ R^D^O RcDcL RcOc[ RiDiL RcS]Y RcSiXlY RZVRY RZTZX',
	    'Po^FlF RaLlL RSQZQ RbTjT RSHSS RWCWX RSHZHZQ RbObT RjOjU RcHdK RcCeE ReWb[ RZTZWRY R^F^T][ RiHgLfTeX',
	    'PodHjH RSQ[Q RbWhW Ri[l[ RSHSR RWCWX RSH[H\\Q R^N^[ RbQbX RdHdN RbQhQhW R`DjDjN R`D_NlNl[ RZT\\YRY',
	    'Po_IiI R_NiN RSR[R R\\[l[ RSISS RWDWY RSI[I[R R^R^[ R_E_N RbRb[ RfRf[ R_EiEiN R^RjRk[ R[U[YRY',
	    'Po^MlM RSQ[Q RbWjW R`WhW Ri[l[ RSHSS RWCWX RSH[H[Q R]Q][ R^H^M R^EeEeV R^HlHlM R]QlQl[ RhSjX RZT[Z R[WRY RkCbD',
	    'Po]GlG R`QiQ RSQ[Q R`TiT Rg[k[ RSHSS RWCWX RSH[H[Q R^K^[ RdQdZ R^KkKk[ RbLbO RgLeQ RZT[Z R[WRY RdDdK',
	    'Po]IlI RSQ[Q R\\SlS RSHSR RWDWX RSH[H[Q RdCdO R_D`H RiDhH RdH^N RdHhLlN RbP_WkZ RhSgVdY\\[ RZT[XRY',
	    'Po^FjF R^JiJ R]NlN RSP[P R`UiU R`ZiZ RSHSR RWCWW RSH[H[P R`P`[ R_RiQi[ RfNlS RaO\\S RdH_R R[S\\Y RdDaO R[VRX',
	    'Po\\FlF R]PkP RSQ[Q RSHSS RWCWX RSH[H[Q R]J]N RaCaI RdLd[ RgCgI R]JkJkN RfSlY RaT\\Y RePgU RbP`V RZT[XRY',
	    'Po^HjH R^LjL RSQ[Q R]SkS R]WkW Rd[l[ RSHSS RWCWY RSH[H[Q R]O]X R^D^L ReDe[ R^DjDjL R]OkOkW RlYl[ RZT[YRY',
	    'Po\\KlK RdPgP RSZkZ RYCYP R_P_Y RgIgP RURiRiV RURUViWl[ RTEVH R^MbN RcC]F RYKRM R`EiFdH\\J',
	    'PobIjI RbNjN RSQZQ Rb[l[ RSHSS RVDVY RSHZHZQ RbEbW RbEjEjN RePjV R[M^M^X[[ RjPgS R\\E]I R^Xd[ RfUaW RYTZYRY R^M[M',
	    'Po]KcK R]PcP RSPZP RSHSR RWCWW RSHZHZP R]G]Y R]GcGcP RfEf[ RdRaU RjNlT R`C`G RfElEiM R_SbVcX[Y RlWhW RZS[XRX',
	    'Po]FlF R\\PlP RSQZQ Rd[h[ RSISR RWCWX RSIZI[Q R]K]N RdCdF RdMdP R]KkKlN RhGgJ R`W\\[ R`GaJ RbP`X RaTjTh[ RYT[XRX',
	    'PocElE R\\MaM RSPYP R\\XaX RSHSR RVCVX RSHYHYP R\\G\\[ R\\QaQaY R^C]GaGaM RdJdW RgEg[ RdJkJkViV RYSYXRX',
	    'Po_JjJ R_NjN RSQ[Q R_RjR Ra[h[ RSHSR RWCWX RSH[H[Q R_F_R RaUa[ RdCdF RhXh[ R_FjFjR RcSfV RiUlZ R^V][ R[T[XRX',
	    'Po\\HcH ReHlH RSQZQ RSHSR RWCWX RSHZHZQ R`C`H RhChH R\\P`S RdPhT RhJeP RjNeZ R`J]P RbN]Z RYTZXRX RbVbZ[Z RjVkZdZ',
	    'PoSQZQ R]QkQ R\\UlU RSISR RWCWX RSIZIZQ RcFcI R^LaO RgNkR RhEkH RfK`Q RbI_M RdVgYl[ R^E\\J RcSbW`Y[[ RYTZXRX RiC^E',
	    'Po]DlD R`MjM RSPZP R`QjQ Rb[e[ RSHSR RWDWW RSHZH[P R`I`Q RdFdI ReQe[ R`IjIjQ RiTkY RaT_Y RZSZWRX R]D]S\\Z',
	    'Po^MiM RSQZQ R_[b[ Rg[j[ RSISR RWCWX RSIZIZQ R\\PbPb[ RdPkPk[ RfJjM RbEgDlI RaP]T RbS]Y RiPeT RjSeY R`F\\J RbH`M RYTZXRY',
	    'Po]HlH R]OlO RSQ[Q R]SjS RSHSS RWCWW RSH[H[Q RaHa[ RfGf[ R]KjKjS RfUlZ R_DaF R_W\\Z RgDeI RaQ^Y RZS[WRY',
	    'PofJjJ R^NaN RfNjN RSQ[Q RSISR RWCWX RSI[I[Q R^F^N RdCdR RfEjEjN R_ScXk[ R]RjReW][ RbD^F RaJ]J RZT[XRX',
	    'PoRJ]J RSZkZ RUEUM RYKYN R_Q_Z RbDaH RVRiRiV RhHlGlH RVRVVhWl[ RYFYH RbKdNjP R`JjJgMaP RbDhDhH RUE]E]PZP',
	    'PoRE_E RTL]L RaRkR RZS_S RUU\\U R[[_[ RROR[ RTHTL RYUYZ RTH]H]L RRO_O_[ RaIaR RfCfZ RaIkIkR RZOZS RWOVS RiUkZ`Z',
	    'Po]ElE R_LjL RSQ[Q RfSlS RaUiU RSISR RWCWX RSI[I[Q R^O^[ R_H_L RdUdZ R_HjHjL RcOaS_S RfOfS R^OlOk[h[ RZT[XRX',
	    'Po_GlG RbLkL RSQZQ R`RlR RSHSS RVCVX RSHZHZQ RhWl[ R_R[S ReV`[ R]K]N RfLfSiY ReCfG RfRcY RcIaO RYTZYRY R_G_R]Y RfOeW',
	    'Po_KiK R_OlO RSQ[Q Rf[j[ RSHSR RWDWX RSH[H[Q RaUbY R_FiFiK RfUiW RcCbF RdUeX R^V]Z RZT[XRX R_F_SkSj[',
	    'Po^IjI R^MjM R^PjP RSQ[Q R\\TlT RSHSS RWCWX RSH[H[Q R^F^P R^FjFjP RgRiS RdCcF RdSeWgYl[ RcV`Y[[ RdPbX R[T[XRY',
	    'PoRG]G RSN\\N RSR\\R R`SjS RRV]V RSJSR RXCXJ RXRX[ RSJ\\J\\R R`N`S R`NjNkS ReDkK ReE^K RiUk[ ReJfY^Z RjX`Y',
	    'Po\\FkF R]JjJ R\\MlM RSQZQ R]UjU RSISR RWCWX RSIZIZQ RdQd[ RgMlR RfJhP R`N\\S RdThYlZ RbH_Q RdT_Y\\Z RcD`N RgP_Q RYT[XRX',
	    'PofHkH RSQZQ RfRkR Rh[l[ RSHSS RWCWX RSHZHZQ RfLfR RfFfK RdThTi[ RaKkKkN RlXl[ RdTcZ RZTZXRY R]F]S\\Z RaK`S_Z RjD_E RhNbO',
	    'Po^NkN RSQ[Q R_TdT Rf[l[ RSISS RWCWX RSI[I[Q R^F^N R_Q_[ RfQf[ R^FkFkN RlXl[ RdCcF RaIgL RgHdK`M RlRfU RdY][ RYT[XRY',
	    'PoSQ[Q R_SjS R_VjV Rf[i[ RSHSS RWCWX RSH[H[Q R]L]O R_O_[ R`D`L R`DiDiL R_OjOj[ R]LlLlO R`HeHeL R[T[YRY',
	    'Po]FlF R]MlM RSQZQ R_QjQ R_ZjZ RSISR RWDWX RSIZIZQ R_T_[ RdCdF RdHdS R_JjJjQ R_TjTj[ RYTZXRX R]F]R\\Z',
	    'Po^IjI R]LlL RSQ[Q R_UjU R\\[l[ RSISS RWCWX RSI[I[Q R^E^I R_P_U RdCdI R_PjPjU RkEkI RaVaZ RhVgZ RYT[YRY',
	    'PoTGjG R`UjU RSV]V R_ZkZ RSQSU RXOXZ RSQ]Q]U R_C_N R`Q`U ReNeZ R`QjQkU RRZ]Y^[ RiWl[ R_FbIkM R_F[JSM',
	    'PoRG^G RTL]L RTO]O RYR]R RSZkZ RTJTR RTJ]J]Q R_R_Z RUTiTiW RbCbJkHkJ RUTUWiWl[ R[E_H RVCRH RbKbQkPlQ RjDaF RjLaN',
	    'Po_JkJ R`NjN R^QlQ RSQ\\Q R`ZjZ RSHSR RXCXX RSH\\H\\Q R^G^J R`T`[ ReHeT R`TjTj[ R^FlFlI RdCgF R[T]YRY',
	    'PoRF^F RSI]I RhIlI RTO]O RWZjZ RTZgZ RXCXI RRLRM^L^N R_Q_Z RbDhDhI RUSiSiW RUSUWhWk[ RaKdNjP R`KjKgNaQ RbDaH',
	    'Po^KkK R^PkP RSQ[Q RSHSS RWCWX RSH[H[Q R^F^P R^FkFkP RePe[lZl[ RaV\\[ RhRfW RcP`X RZT[Z R[WRY RjTkWfW RdCcFdKbT',
	    'Po^EkE R^IjI R^MjM RSQ[Q Rf[j[ RSHSR RWCWX RSH[H[Q RbTbY ReEeQ RgSiU ReTeX R^U]Z RZT[XRX R^E^QlQk[',
	    'Po^MjM R^QjQ RSQ[Q R\\TlT RSHSR RWDWX RSH[H[Q R]E]H R^I^P RdQdT R^IjIjP R]EkEkH RgWk[ RaW\\[ RZT[XRX',
	    'PocEgE R`JhJ RSQZQ R\\[l[ RSISS RWCWX RSIZI[Q R^T^[ RbTb[ RfTf[ R^TiTi[ RhPlT RhFkJ RbNgNjS R`E^J R_N]T RYT[XRY',
	    'PoSFkF RSV]V R`VjV R_ZkZ RRZ]Z RSQSU RXOXZ RSQ]Q]U R`Q`U ReOeZ R`QjQjU R\\W][ RiWlZ R^C_F RYH_KiM ReG_KUM R^KkN R`KRM',
	    'PoVUiU RSZkZ RSLSP RVQVU R_N_Z RVQiQiU RSLlLlP RhXk[ RkEiG RUESG R]E[G RbE`G RXH]J ReHlK RfDdI_J RWIRK RYCXI',
	    'Po^IiI R\\MlM R^ViV R_ZkZ R]ZgZ R^R^V RdOdZ R^RjQjV RhWk[ RVIXL RVOYR RgMlR RgDlI R`M[R R_E\\I RfIiP RbH_P RcC`N RTEZEZ[W[ RTETSS[',
	    'PoRF^F R`LkL R`RkR RSU]U RSQSU RSQ]Q]U R`G`R R`GkGkR RgVl[ RdV`[ R\\W^[ RXCXF RfCeRiY RTH^M ReRbZ R[HXLRN RfOfU RfLdW RXNXYRZ R[YTZ',
	    'Po_OjO RSQZQ R_RjR R_ZjZ RSHSS RWCWX RSHZHZQ R_L_[ R]LkKjR R^UjUj[ RcHeJ RdEkL RcCbG]L RYTZYRY',
	    'Po]FkF R^IjI R\\LlL R^PeP RSQ[Q R]TkT Ra[d[ RSHSS RWCWX RSH[H[Q RdCdL RdTd[ RgVkZ RhRlU R`V\\Z RcN^P RgO]T RZTZXRY',
	    'Po]FgF R\\IlI RSQ[Q R_WjW R_ZjZ RSISR RWCWX RSI[I[Q R_S_[ RbCbI R_SjSj[ RjEgH\\M RZT[XRY RiLaLaPjOkP',
	    'Po\\FlF R\\JkJ RSQZQ Rf[k[ RSHSR RWCWX RSHZHZQ R]N][ RaCaH RbQcU RgCgH R]NkNk[ RfJfRhU RYTZXRX RbJbOaV RfMeW',
	    'PoSQZQ RbSlS Rd[g[ RSHSS RVCVX RSHZHZQ R^H^[ R`H`V RgOg[ RjUkY ReIjO RjIdO RdGfL RdGjGhK RdUbY R`C[M ReCbJ RYT[Z RZWRY',
	    'Po]FlF RSQ[Q RbWgW Rh[k[ RSHSS RWCWX RSH[H[Q R^L^[ RbRbY RbRgRgW R^LkLk[ RfNiQ RcDeF RcN`Q RaHbK RhHfL RZT[YRY',
	    'PoSE^E RbGlG RRH_H RXO[O RSZjZ R[H[O R_P_Z RVRiRiV RVRVVhVk[ R]J_L RTJRL RcFdJhNlO RdCbH RUNSO RiHeL`O RYCXHVN',
	    'Po\\FlF R]MkM R]QkQ RSQ[Q R^WjW R^ZjZ RSHSS RWCWX RSH[H[Q R]I]P R^S^[ RbCbP RgCgP R^SjSj[ R]IkIkP RZT[YRY',
	    'Po\\FlF R^OjO RSQ[Q R_WhW Rg[k[ RSISR RWCWX RSI[I[Q R]R][ R^H^O RdCdF RjHjO R]RkRk[ RgUhX RaIhL RgHcL`M RcOaW RYT[XRX',
	    'Po[GlG RSQZQ RgYjY RSISR RWCWX RSIZIZQ R]N]R^Y RdOd[ RfCfK R^RjRjY R\\NkNkQ RiCiKlK RbCbKfK R_E^K RYT[XRY',
	    'PoTE]E RRG_G RSJ_J RYP]P RTZjZ RYCYJ RWM]M]P R_P_Z RVRiRiV RVRVVhWk[ RWLSP RcFdJhNlO RcCaI RbGkGhJeM`O',
	    'Po]FlF R]OkO RSQZQ R_TdT Rf[l[ RSHSS RWCWX RSHZHZQ R_Q_[ RaFaO RfQf[ RfFfO R]JkJkO RlXl[ RcCdF RkSfU RZTZXRY RdZ_Z R]F]T[[',
	    'PoTG^G RaGlG RWI^I RUL`L RSZjZ RZLZQ RTD^D^G R_P_Z RVRiRiV RVRVVhWk[ R]M_O RbJeL RWMUP RhChPeP RTDTJSP',
	    'Po]MkM RSRZR RbRjR R^VjV R\\[l[ RSISS RWDWY RSIZIZQ R]F]I R^ReRd[ R]FlFkI RgPkS RhIjK RdCeF RbM]S RbI]K RZU[YRY',
	    'PoSQ[Q R_QiQ R^VkV R\\[l[ RSISS RWDWY RSI[I[Q R]H]L R_L_Q RdCdH RdQd[ R_LiLiQ R]HlHlL R_D`G RjDhG RZU[YRY',
	    'Po[EkE R\\JiJ RUWhW RU[i[ RS[g[ RRDRQ R^Q^Z RcCcQ RUShShW R\\GiGiM RVIYL RRDXEUH R\\G\\MfMlP RXNUN RcMZP RUSUW',
	    'Po\\ElE R]MkM R^PjP RSQZQ R\\TlT Ra[d[ RSHSS RVCVX RSHZHZQ R]H]M RbEbM RdTd[ RfEfM R]HkHkM RhVlZ R`V\\Z RZT[XRY',
	    'PoUDjD RUKhK RUNjN RSV]V R`VjV R_ZkZ RRZ]Z RSQSV RUDUN RXOXZ RSQ]Q]V R`Q`V ReOeZ RUHhHhK R`QjQjV R\\W][ RiXl[',
	    'PoTEkE RYKjK RYMjM RWPlP RYWiW RY[k[ RW[h[ RYHYM RYTYW RaRaZ ReMeR RYTiTiW RYHjHjM R`MZP RaH`M RTETSS[',
	    'PoUO^O R\\[k[ RT[b[ RUKUP R_Q_Z RVRiRiV RVRVVhWk[ RYG]I_K R[D_F ReJkO ReKaO RcFlFjI RdE`H RfHcM RWERG RUK^K^O RYFTKRK',
	    'Po[FcF R\\NbN RSQYQ R\\SbS R[VcV RSHSS RVCVX RSHYHYQ R\\J\\S R_C_[ R\\JbJcS ReLkLj[ RXTYYRY RlEeFeSdZ',
	    'Po^HkH R^LkL RSQ[Q R\\UkU RSHSR RWCWX RSH[H[Q R^E^L RdTd[ R^EkEkL RhWkZ RhRlU RdEdL`PbR_T RgNbS R`W\\Z RZT[XRY',
	    'Po[HlH R^KiK RSQZQ R]QjQ R\\TlT RSHSR RWCWX RSHZHZQ R]N]Q R^E^K R^EjEjK R]NjNjQ RdCdQaT^XlZ RYTZXRX RiTeY\\[',
	    'Po\\FlF R`JhJ R^PkP RSRZR R]TkT R^WkW R\\[l[ RSISS RVDVY RSIZIZR R^L^P R`C`I RdJd[ RhChI R^LkLkP RYUZYRZ',
	    'PoSV]V R`VjV RiXl[ R_ZkZ RRZ]Z RSQSV RXOXZ RSQ]Q]V R`Q`V ReOeZ R`QjQjV R\\X^[ R\\KbK R\\DTG RYF_IcJkK RZEeE_H[JRK RYMcN',
	    'Po_JjJ RSQ[Q R^RkR R^VkV RSISR RWCWX RSI[I[Q R]F]J R^N^V RdCdG RdJdV R^NkNkV R]FlFlJ RgXkZ RbX\\[ RYT[XRX',
	    'PoaEjE RTHYH RRKYK R\\KlK RdPgP RSZkZ RZCZP R_P_Z RhIhP RVRiRiV RVRVVhWl[ R^MbN R^FcH RiFbI RdC]F RTDTH RUKSQ RaEfH\\J',
	    'Po\\FlF RSQ[Q R`RgR R\\UlU RSHSR RWCWX RSH[H[Q RdCdG RdSd[ RfOgR RhNlR RiIkK R_N[R ReKcO R`KcN`Q R_I\\K RcHbL RZT[YRY',
	    'PoVHhH RVKkK RVO`O RSO]O R`VjV RSV]V RRZ]Z R_ZkZ RVDVO RXPXZ RSR]R]V RePeZ RVDhDhH R[X^Z RiWl[ R`JcMhO R`RjRjV RSRTV R`R`V RkMkOhO',
	    'Po]HhH R]KhK R]MlM RSQZQ R\\ShS RgVkV RSHSS RVCVX RSHZHZQ RcQc[ R]FhFhJ R]F]PkPkV RcS\\Z RbCaF RcSgXkY RZS[WRY',
	    'Po^GjG R^JjJ R]QkQ RSQZQ RSHSS RVCVX RSHZHZQ R]M]Q R^D^J RaMaQ RfMfQ R^DjDjJ R]MkMkQ R^TbWfYl[ R]TjTgWbY[[ RZTZXRY',
	    'PoTE]E RRH_H RRK^K RSM^M RUViV RSZjZ RURUV RXCXH RXKXP R_Q_Z RURiRiV RhXk[ RUIVK R`IfL R\\H[K RaN^O RlMkPlO R`FiFiKiP RdCcL',
	    'Po^GlG R_MlM RSQZQ RcSiS RSHSS RVCVX RSHZHZQ RcIcS RcVcZ RdCeG RiIiS RgVgZ RjUlZ R`V_[ RZTZYRY R^G^S\\Z',
	    'Po]FlF R^MjM RSP[P R^QjQ R]UlU RSHSR RWCWW RSH[H[P R^J^Q RaCaH RgCgH R^JjJjQ ReVhYl[ RcV`Y\\[ RdQbX R[S[WRX',
	    'PoRElE RVKhK RVNhN RRQlQ RWWgW RVHVN RXRWW RZCZG R_Q_Z RdCdG RVHhHhN R]NWTgSlU RUZgYk[ RYRRU RgRgW',
	    'Po_JjJ RSP[P R`QdQ R]ZlZ RSHSR RWCWX RSH[H[P R]E][ R`M`Q R`MdMdQ RjUjX R]ElEl[ RhGiH RgSbX RjLfU RdT_T RfFfOhW RZTZXRX',
	    'PoSQZQ RSISR RWCWX RSIZIZQ R\\DbDbL RdDjDjM RhEeI RiIeL R_E\\H RaI\\M ReOlR RcM`P[R RcR^T RfT^X RhWcY\\[ RYT[XRY',
	    'Po\\O`O R`UjU RSV]V RRZ]Z R_ZkZ RSQSU RXOXZ RSQ]Q]U R`I`N R`Q`U ReOeZ R`QjQkU R\\X^[ RiWl[ RSIjIfL R`IUN RZFbH RVDgD_G',
	    'Po]KlK RSQZQ Ra[h[ RSHSS RWCWY RSHZHZQ RaTa[ R^EdEdP RhXh[ RcRfU RdJkQ RdJ]Q RiFhI R_G`I RiTlZ R^U]Z RjD`E RZT[YRY',
	    'PoRRZR R^RkR Ra[i[ RRIRS RVDVY RRIZIZR R^F^R RaUa[ R^FkFkR RcTfV RdLhO RiXh[ RcCbF RcH`K RiTlY R^U]Z RbJhJdN`P RYTZXRY',
	    'Po]HdH RhPkP RSQZQ R\\SkS RSHSS RWCWX RSHZHZQ RaDaO RcQc[ RgEgL RkCkP R`HdM RcR]Y RaH\\N RcShXlY RZSZWRY RdD]E',
	    'Po\\FlF R`JhJ RSPZP Rh[k[ RSHSR RWCWX RSHZHZP R]M][ R`C`J RdJd[ RhChJ R]MkMk[ RhQfW RhQiW RaQ_W RaQbW RaQ_P RhQeP RZTZXRX',
	    'Po\\ElE R\\MlM R^PjP RSQYQ RSHSS RVCVX RSHZHZQ R`C`G RcGc[ RfCfG R^JjJjP RjRj[ RfSgY RaS_Y RZTZXRY R]S\\Z',
	    'Po`PiP RSQ[Q RSHSR RWCWX RSH[H[Q R]H]K R^S^Y R`K`P RdCdH RdPd[ R`KjKjP R]HlHlK R_D`H RjDiH R^SkSkYhY RZT[YRY',
	    'Po]FkF R\\JlJ R^PjP RSQZQ R^SjS R\\WlW RSHSR RVCVX RSHZHZQ R^M^S RdSd[ R^MjMjS RgGfJ RcCdF RYT[Z RZWRY RaGaI',
	    'Po\\FlF R\\KlK RSQ[Q R\\TlT RSHSR RWDWX RSH[H[Q RaCaH RgQg[ RgCgH R`W\\[ RdKhOlQ RdHbM_P\\Q RaR`X RZT[XRX',
	    'Po^NkN RSQZQ RSHSS RVCVX RSHZHZQ R_H_M RfSfZ R]IkIkM RaE\\J RhRkY ReLbO]Q RdR]U RcOfTbW\\Y Re[a[ R`FhEeIdN RkPeRfX RYTZXRY',
	    'Po]MjM R]QjQ RSQYQ R\\UkU RSHSS RVCVX RSHYHZQ R]E]M RaWb[ RdEdT R]EjEjM RjXl[ ReWf[ R_GaK R^W\\[ RgGfK RYTZZ RZWRY',
	    'PoSPZP R^SjS R^WjW Rg[j[ RSHSR RWCWX RSHZHZP R]E]K R^P^X R`C`K RePeW R]PjPj[ RfCfKlJkK RaL\\Q R`NgMdP RkFfG R\\ZZ[ RdG`GdG RdJ\\K RYSZXRX',
	    'Po[GcG R]KaK ReLlL R]PaP RSQYQ RZTcT RSISR RVCVX RSIYIYQ R]C]T RaCaT RiLi[ RaWcY R\\VZ[ RXTYXRX RkDeEeRcZ',
	    'PoRElE RUIiI RVOhO RTRjR RRZkZ RUGUJ R_C_L R_M_R R_S_Z RUGiGiJ RUUiUiX RRLlLlO RUUUXiXkZ RRLRO',
	    'Po^QjQ RSQZQ R^TjT R^WjW RSHSS RWCWX RSHZHZQ R^N^W R`E`L R^NjNjW R_E\\I RfYl[ RgDjE RaY\\[ RdDfJ RZT[WRY RkGfGaH RkJkLiL',
	    'Po[GeG R[LlL RSQZQ R]TcT R]XcX RSISR RVCVX RSIZIZQ R]P]Y R]H^K R`C`F R]PcPcX RhUb[ RiEjI RkOgV RlWk[ RcHbK RYTZXRX RgCgNgQhZ',
	    'PoRE`E RSG`G RTL_L RWP^P RSPZP RSZkZ RTITL R_I_L R_P_Z RURiRiV RURUViWl[ R]M[O RVMVP RYCYG R[I_I RWITI RjDbF RjHbJ RlLgNaO R\\IYI RWIZI',
	    'Po\\FfF R\\JeJ RSQZQ R]RdR RSHSS RVCVX RSHZHZQ R]M]R RaCaJ R]MdMdR RkEfI RkMfQ RkUe[ R^T_W RcSaX RYTZXRY ReX\\Y',
	    'Po]HlH R`OhO RSQZQ RaXgX Rh[j[ RSISR RWCWX RSIZI[Q R]R][ RaJaO RaTaY R_LhKhO R]RkRk[ RfHjL RcG]M RcDaJ RaTgTgX RZT[XRY RiC_E',
	    'PobDkD RbLjL RbOlO RSQZQ R\\SlS RSHSS RWCWX RSHZHZQ RbDbO RcQc[ RbHjHjL R[H_I R]D`F R`L\\P RcSgWlY RcS_X[Y RYTZXRY',
	    'PobGlG RRZjZ RRHRP RUJTN RXCXP RRH^H^O R^Q^Z RTSiSiV RTSTVhWk[ RRDUF R]D[F RcE_I RZJ[N RbFcJgNlP RiFhJeM`O',
	    'Po\\FlF R_PiP RSQZQ R_TiT Ra[d[ RSISR RWCWX RSIZIZQ R_L_T RdTd[ R^MiLiT RhVkZ R]I_K RfIkN ReFgK RkIhK R`V\\Z RaI\\N RcC`K RZT[XRX',
	    'Po\\KlK RSQ[Q R^VjV R^ZjZ RSHSR RWCWX RSH[H[Q R^R^[ R]EdEeP RdRdZ R^RjRj[ R_F`I RdJiOlP RdJ^P RiFgJ RiDaE RZT[YRY',
	    'Po^EdE RSQZQ Rg[k[ RSHSS RWCWX RSHZH[Q RcVcZ RhUiW ReUfY R^F_I RcFcI R^OjOjS R_V^Z RiFhI R^KhKhO RYTZXRY R^K^TlSk[ R^O\\Y RiDaE',
	    'Po\\FlF R]IkI RSQ[Q R\\SlS R^ZjZ RSHSS RWCWY RSH[H[Q R^U^[ RdCdI R_KjKjO R^UjUj[ R_OiOgR RaPaR R_K_O RZT[YRY',
	    'Po\\NbN RSQZQ R]QlQ RSISR RWCWX RSIZIZQ RfXi[ R\\H_J ReHhJ RjKkN RlXk[ RaLbO ReNiNjP R^UaV RjFgL R_D]H RaG]N RhDfH RjSeX_[ RYTZXRY RdCdKfX R]Q\\Y',
	    'Po]HbH ReHkH R]LcL RSQZQ R]QkQ R\\VlV RSHSR RWCWX RSHZHZQ R^D^L RaNaV R]DbDbH RgNgV ReDkDkH RfXkZ RbX\\[ RYTZXRX RfDfLkKkL',
	    'Po]FlF RaIhI R\\LlL RSQ[Q R^SjS R^WjW RSISR RWCWX RSI[I[Q R^O^V RaCaI RdLdV RhChI R^OjOjV RgXl[ RaX\\[ RYT[XRX',
	    'Po]EkE R\\IlI R^PjP RSQZQ R^SjS R^WjW RSISR RWCWX RSIZI[Q R^M^V R`G`K RdCdH RhGhK R^MjMjV RfXl[ RaX[[ RYT[XRX',
	    'PoSQZQ Rd[i[ RSHSS RWCWX RSHZHZQ R]N]Q R]NlNlQ R`HcK RhHlL RkEjH R]E]H RcEcH ReEeH R^K\\L RfKdL RdPbW^Z\\[ R`C_J RhCgJ RZT[XRY R]SjSi[',
	    'PoUDlD RWIaI RYWhW RVZkZ RYSYW RXKXN`M`N RaQaZ RYShShW RfMjQ RfMcQ RdIkIiK R\\NXQ RdGbK RfJeO RUDURSZ R_G\\J[P',
	    'Po]ElE R_KeK RSQZQ R`TeT RSHSR RVCVX RSHZHZQ R`M`T ReMeT R`G`J RYT[[ RgKkKkP RZXRY RiOjY R]E]T[[ RdGbKbTaY RgGfP RiNgX',
	    'Po\\FlF R^LjL RSQ[Q R]SkS RSISR RWDWX RSI[I[Q RdCdS RbVa[gZg[ R^IjIjO RjWk[ R^I^OiPlT RbTfV R^V\\[ RZS[XRY',
	    'Po\\DlD RbKfK RSQ[Q R^QjQ R^TjT R\\WlW RSISR RWCWX RSI[I[Q R]G]L R^N^T RbDbK RdTd[ RfDfK R^NjNjT R]GkGkL RYT[XRY',
	    'Po]IcI RfIkI R^OkO RSQ[Q R^SkS R]WlW RSISR RWCWX RSI[I[Q R]D]I R^L^S R]DcDcI RdLd[ RfDfI R^LkLkS RfDkDkI RZT[XRX',
	    'Po^IkI R^LkL R^PkP RSQ[Q R^TlT RSISR RWCWX RSI[I[Q R_G_T RaWb[ ReGeT R_H[M ReWf[ RjVk[ R`C]K ReDeI R^W\\[ RZT[XRX',
	    'Po]GlG R^JjJ R]MlM RSQZQ R\\RlR R^ZjZ RSHSS RWCWX RSHZHZQ R^U^[ RdGdR R^UjUj[ R`DaG R_N_Q RiNhR RhCgG RZT[YRY',
	    'Po^FjF R\\JlJ R]MbM ReMkM RSQZQ RdQlQ R\\TlT RSHSR RWCWX RSHZHZQ R`K`Q RdCdJ RhKhQ RgTf[lZl[ R_Y[[ RaT_Y RbQ\\Q RYTZXRY',
	    'Po\\HlH R^KjK RSQZQ R]TkT RgVkV RSHSS RWCWX RSHZHZQ R]M]WaVa[ RdCd[ RgVg[ R^EjEkK RkMk[ RZT[Z RgMgQkQ RaMaQ]Q RZWRY R]R\\Y',
	    'Po\\NlN RSQZQ R\\TlT RSHSR RWCWX RSHZHZQ R_F_T RaWa[ RbGbT RfGfT R^HjGiT ReWg[ RiVl[ R_C\\K R]V\\[ RYTZXRX',
	    'PoVIhI RSMiM R`UjU RSV]V RRZ]Z R_ZkZ RSQSU RVEVI RXNXZ RSQ]Q]U R_C_M R`Q`U ReNeZ RVEhEhI R`QjQkU R\\X^[ RiWl[ ReKjM',
	    'Po\\HlH R^KiK R\\NcN RSQ[Q ReRkR R\\UlU Rd[g[ RSISR RWCWX RSI[I[Q ReMeQ RhSh[ R^DiDiK ReMkMkQ R_VbY R_N_R RcQ\\R RYT[XRX',
	    'PoRE_E RUH]H RbIlI RUK]K RRN_N RY[j[ RT[f[ RUCUM R]C]M R_R_Z RhIhQ RUSiSiW RVTVWhXk[ R\\O]P RVOSQ RbEbJaO RjDbE',
	    'Po\\ElE R^HdH ReMlM RSQYQ R^RcR Rg[j[ RSISR RVCVX RSIYIYQ R^L^Q R_T_X R^LcLcQ RjHj[ RfQgU RbTbX RXTZYRY RdY]Z R\\E\\SZ[',
	    'Po]HcH R]KcK RfNkN RSQZQ R]VkV R[[l[ RSHSR RWCWX RSHZHZQ R]E]O R]SdRd[ RfFfN RfFkFkN R\\EdEcO\\O RZT[XRY RiQ`R',
	    'Po`PiP RSQ[Q R^VkV R^ZkZ RSHSR RWCWX RSH[H[Q R]H]K R^R^[ R`K`P ReCeH ReReZ R`KiKiP R^RkRk[ R]HlHlK R_D`G RkDhG R[T[YRY',
	    'PoSEkE R`VjV RSV]V R_ZkZ RSQSV RXOXZ RSQ]Q]V R`Q`V ReOeZ R`QjQkV RRZ]Y^[ RiWl[ R`EaHeLkN RiFcI R^HTK R\\F_I`K[MSO R`O\\O RaETH',
	    'PoTJ^J RaKkK RTN^N R_NlN RZQ]Q RSZjZ RZFZM RTG^G^Q_Z RfIfQ RVRVVhWk[ RUE[EYH ReDaI`M RVRiRiW R`DkDjHhH RWCSGTLSP',
	    'Po\\FlF RaNgN R^PjP RSQ[Q R_ViV R_XiX R[[l[ RSISS RWCWX RSI[I[Q R^I^P R_S_X RaKaN RdCdF RaKfKgN R_SiSiX R^IjIjP RYT[YRY',
	    'Po]FkF R]JkJ R\\MlM RSQZQ R\\SlS R][`[ RSHSR RWCWX RSHZHZQ R`O`[ RdFdM RlXl[ RgXj[ RiOkQ R_DaE RhCfE RjUfYbZ RYT[XRX RcV\\W RfOgY RcO\\P',
	    'PoSE_E RUN[N RZP]P RTZjZ RUKUO RUK[K[N R\\C\\G R_Q_Z RVRVVhWk[ RVGRK RbFdJgNlO RVCVG RcCaI RbGkFhJeM_O RVRiSiW RUI^I]P',
	    'PoaFiF R`JjJ R`OjO RSQZQ RaRiR R`UiU Rb[l[ RSHSS RWCWX RSHZHZQ RbKbN ReCeJ ReOeY R]F^I R[O^O^Xc[ RgKgO R^YZZ RYTZXRY',
	    'Po^IlI RgLlL RaNjN R^QlQ RSQZQ RaSjS RaUjU R`ZkZ RSHSR RWCWX RSHZHZQ R`W`[ RgIgL R`WkWk[ RcJ`M R`FgEfH R`F[K RYTZXRX RaC^I^R\\Y',
	    'PoRE^E RhHkH RSI]I RSK]K RRM^M RTP]P RSZjZ RSGSK RXCXP RSG]G]K R_Q_Z RbDaH RURiRiV RURUVhWk[ RbDhDhH RcMgOlP R`JjJgMdO_P',
	    'Po^QjQ RSQZQ R^TjT RSHSS RWCWX RSHZHZQ R^NjNjX RbFkFiH RcD`H R^N^XiYlZ ReHhKlL R]D_E R`J\\K ReHcK_L RbY\\[ RZT[YRY',
	    'Po\\GcG R\\JcJ R\\PcP RWWhW RUZjZ RS[g[ R\\K\\P R\\E\\J R_Q_Z R\\EcEcJ RfDfP RRFVI RiIlL RVCTG RXGRLXLTQ ReXk[ RfDlDjI RWShShW RYJWO RWSWW RlNhO RbL\\M',
	    'Po[ElE R\\JkJ RcNhN R^QjQ RSQZQ R[TlT RSHSS RVCVX RSHZHZQ R^H^P R`C`G RcHcN RcQc[ RgCgG RhHhN RcS^Y RcSgWlY RYTZYRY',
	    'PoTEkE RUHjH RVMiM RTSYS RcTgT R[VaV RTVYV RV[X[ RTETH RVJUM RTPYPY[ R[Q[V R^O^Y R[QaQaV RVJiJhM RcPiPjY RlXl[ RTPTVSZ RePeUdY RaWaYZZ',
	    'Po^JhJ R]PiP RSQZQ Rf[j[ RSHSS RVCVX RSHZHZQ R\\S\\[ R]K]P RjKjP R\\SkSk[ RcGcJ RaKgN RcDlI RcP`X ReL_N RcC`G[I RYTYYRY RfUgX_X',
	    'PofIkI R]IbI R]MbM RSQZQ R]RbR RfRkR R]WbW RfWkW Rc[l[ RSHSR RWCWX RSHZHZQ R]M]X R]E]I RcEc[ RfEf[ R]EkEkI RfMkMkV RlYl[ RYTZXRY',
	    'PoREkE RUKhK RWPfQkR RRZkZ RSMSR R_G_O R_R_Z RUSiSiW RSMkMkQ RUSUWiWl[ RcNgO RUGhGhK ReIhI RUGUJYI RbI^I[IXI ReIcI RdCdF',
	    'Po\\FlF R^MjM R^PjP RSQ[Q RgUiY R_WiW Rg[k[ RSISR RWCWX RSI[I[Q R]S][ R^I^P RaCaH RdIdW RgCgH R^IjIjP R]SkSk[ RZT[XRX',
	    'Po\\FlF RSG[G RSJ[J R]KdK RSM[M RXZjZ RSZfZ RSDSL RSD[D[L R_Q_Z RVShShV RlMlP RVSVVhWj[ RYN[P RhDjE RjIdO RUNRQ R`L]O RhOkP ReCgN R`H`N',
	    'Po]IjI R\\LlL RSQZQ R^SiS RSHSR RWCWX RSHZHZQ R]D]I R^N^S RaUaZ RbDbI RfDfI R^NjNjS R]DjDjI ReVjZ RdSgX RcSaU\\W RkUfW RYTZXRX RfY]Z',
	    'Po]FlF R]KlK R]QlQ RSQZQ R^TjT RSISR RWCWX RSIZIZQ RcHc[ RdCdF RgIg[ R^NjNjS ReTlZ RaU\\Z RbR`W RdH^H RjHeH RYTZXRX R]F]S[Z',
	    'Po^IlI RSQ[Q R_ThT Rf[k[ RSISR RWCWX RSI[I[Q R^E^I R_P_T RbEbI RcNcX RgEgI R_PhPhT R^ElElI RaK\\O R^YhWhY RZT[XRY R_MlMlVk[',
	    'Po]IkI R^LjL R\\PlP RSQZQ R]TkT R\\WlW RSHSS RVCVX RSHZHZQ R]D]I RbDbI RdIdO RdTd[ RfDfI R]DkDkI R`QaT RhQgT RZTZYRY',
	    'Po\\IcI R\\McM RSPZP R\\XaX RSHSR RWCWX RSHZHZP R\\Q\\Z R\\EcEcL R_SaV RhIhTl[ R\\E]M[R R_R^V ReIkIjN Rb[`[ RfCdN RYTZXRX RhLiX R]PcPcVb[ RgNfX',
	    'Po\\FlF R\\JlJ R`PhP RSQ[Q R\\UlU RSHSR RWCWX RSH[H[Q RaCaH RdMdR RhChH RhRh[ RfKjP RbK]P R`X\\[ RcH`N R`S_Y RZT[XRX',
	    'Po\\OcO RSPZP ReQlQ R\\TcT RdUlU R_[b[ RSHSR RWCWX RSHZHZP R[KaJ`T RcJc[ RhMh[ R^FaF_L RgEeL ReNdT RdEkEkKhL RYTZXRX R^D\\J\\T[Z',
	    'Po\\FlF RfNjN RSQZQ R^WiW RSHSR RWCWX RSHZHZQ RdCdF RdJdO RgIgN R^RjQj[ RbGbI RiIlO R^I\\N RfGeI RkIfI R\\IaI`N^N R^P^U]Z RYTZXRY',
	    'PoSQZQ R\\SlS RSHSR RWCWX RSHZHZQ R]N]Q R]F]I RdPd[ R]NkNkQ RdSkY R`HcK RhHlL RkEjH RcT]Z RbEbH ReFeI RfLdL R`D_K RhDgK RYTZXRY',
	    'PoSDkD RSZjZ RVHVR R_D_Q R_R_Z RVThThW RVTVWhWk[ RYN]P RdElI RXJ\\KWL RbJfKaL R[OWQ RbNfOaP RZFRI RUHiHhRfR',
	    'Po\\FlF R_MiM R\\PlP RSPZP R`[d[ RSHSR RWCWW RSHZHZP RaCaH ReVeZ RgCgH R\\J\\KkJkL RfTkY RjSfT RdPgW RdP\\T RcT\\W RaRdTeW[Z RYSZWRX',
	    'Po\\EkE R^KiK R^PiP RSPZP RSHSR RWCWW RSHZHZP R\\M\\P R^H^K RdCcF R^HiHiK R\\MkMkP RfTkY RdPgW RdP\\S RbT\\V RkSfT RcW\\Z RaRcTeWdZa[ RYSZXRX',
	    'Po]EkE R^KbK RfKjK R^MbM RfMjM R\\PlP RSQZQ Ri[k[ RSHSR RWCWX RSHZHZQ R\\H\\K R]T][ RcSc[ RdEdN RgTg[ R]TlTl[ R\\HlHlK RcPbW RZTZYRY',
	    'Po\\ElE R^KkK R^MjM R^OjO RSQ[Q R^RlR RSHSR RWCWX RSH[H[Q R_I_R RaCaG RdIdR RgCgG R_J\\M RaWfYl[ R\\UjTgWcY\\[ RZT[XRY',
	    'Po^PlP RSQZQ R^SkS R^VkV R^ZlZ RSHSS RWCWX RSHZHZQ R_O_[ R]EcEcL RfNfZ ReEkEkL R^F`H RfFhH RcI]L RkIeK RZT[YRY',
	    'Po\\FdF R[JlJ R]OeO RSPZP R]QdQ R]UdU R]XfX RSHSR RVCVX RSHZHZP R]N]Y R`C`J RaMaX RlWl[ RhEjG RgVc[ RjNeY R^K[Q RYTZXRX RfCfMhY',
	    'Po_HiH RSQZQ R^QjQ R^TjT R^WjW RSISR RWCWX RSIZI[Q R^O^W RdCdF RdHdM R]OjOjW R]F]GkFkH RgYl[ RaJ\\L RaY[[ RiL^O RYT[XRX',
	    'PoSEkE RSIkI RXOhO RSV]V R`VjV RiWl[ R_ZkZ RRZ]Z RSRSV RXPXZ RZCZG RSR]R]V R`R`V RdCdG RePeZ RWLhKhO R`RjRjV R\\X^[ R\\IRM RXJXO',
	    'Po\\FlF R^NjN RSQZQ R]RlR RSHSR RWCWX RSHZHZQ R^I^N RaCaH RbIbN RfIfN RgCgH R^IjIjN RlXl[ RfXi[ R_TbW RgOiP RiTeY`[ RdOeX RYTZXRY R]R\\Z',
	    'PoRF[F RbFlF RROlO RXVfV RWCWM RXRXV R_Q_Z RVSgRgV RgCgM RWGZK R^I`L R^D`G RhHlL RgEiJ ReIbL RVHRL RaD]G R`I]L RdOgRlT RUZfYiZ RWEUJ RfEdJ R\\MWRRU',
	    'Po^IjI R^MjM RSQZQ R\\UbU ReUlU R\\ZbZ ReZlZ RSHSS RWCWX RSHZH[Q\\[ R^E^M R_P_Z R\\PbPb[ RdEdM RePe[ RiPiZ R^EjEjM RePlPl[ RZTZYRY',
	    'PoUF_F R`FlF RTM_M RTO[O RfPjP RUR_R RSZkZ RUNUR R_R_Z RbIbR RVTiTiW RbIjIkP RVTVWiWl[ RdEfH RVDRH RbD^H R[K_K RTITLXK RTI_I^L[K R]NWO',
	    'Po]PjP RSQZQ RSISR RWCWX RSIZIZQ R]J]P R^R^Z RdRdZ R]JjJjP R]F_I RcFeI RhFkI RkChF R_C]F ReCbF ReThT R_TbT ReVhW R_VbW RgK`O R`LhN RaZ[Z RhZbZ RYTZXRX RjRkYl[',
	    'PoSDkD R[JcJ R[McM RUOiO RSV]V R_ZkZ RUGUO RXPXZ R[G[O RSR]R]V R_D^G RcGcO RePeZ RUGiGiO RSZ]Y^[ R`R`VjWl[ R`RjRjV RSSSV',
	    'PoXGeG RSIkI RS[^[ R`[k[ RXRXZ RSS]S]W RfRfZ RaSkSkW RaSaWjXl[ RSSSW\\X^[ RZCXG RaJeNlQ RZEeDdI RiKdM R_ISL R]LTN R\\I`L`N[ORQ R`Q\\Q',
	    'PoUEjE RUGiG RRIlI RXNfN RXPfP RSW]W R_ZjZ RSSSW RYKYP RXQXZ RSS]S]W ReQeZ RWLgLfP R`SjSjW RSZ]Y^[ R`WjWl[ RaGeKlN R^C]GXKRN R`SaW',
	    'Po]ElE R_HlH R`NjN RSPZP R`QjQ RaXiX Rh[k[ RSHSR RWCWW RSHZHZP R_S_[ R`K`Q RbFbJ ReKeW RhFhJ R`KjKjQ R_SkSl[ RhUiX RZT[Z RZWRX R]E]S\\Z',
	    'PoTFjF RbLhL RVOhO RVQhQ RSW]W R_ZkZ RSSSW RVIVQ RXRXZ RSS]S]W RbFbL ReReZ RVIhIhQ R`SjSjW RSZ]Y^[ R`S`WjWl[ ReDaG R\\KXM RYD\\G\\K',
	    'Po`FkF R_JlJ RUK]K R`NlN RUP^P RVWhW RUZjZ RS[g[ RVHVP RVSVV RSI^I^K RTDSF^E]F RUN^N_Q_Z RfJfQ RVShSiV RdDfE RiGgJ RcGcJ ReXl[ RXCWGRI',
	    'PodEkE RaJhJ RdLjL R`QjQ RSQZQ R`SjS R^ZlZ RSHSS RWCWX RSHZHZQ R`V`Z R`N`S RcVcZ RdCdG RdHdL ReNeS RgVgZ R`NjNjS R`VjVkZ R^GkGkJ RZTZYRY R^G^T\\[',
	    'PoSEkE RVGhG RVLhL RS[^[ R`[k[ RSISL RYNYZ RSS]S]W R_C_G RYQfQfZ RXOgNgQ RaSkSkW RSIkIkL RaSaWjXl[ RSSSW\\X^[ RYMRQ',
	    'Po[ElE R[KlK R]NjN RSPZP RfSjS RSHSR RWCWX RSHZHZP RaCaG R]P]XaWa[ RdGd[ RfPfS RfWf[ RgCgG R]IjIjN RjPj[ R]UjVfW RYTZXRX RaPaS]R\\Z',
	    'PodGjG R[HaH R\\LaL RcLlL R\\PaP RSQYQ RfUkU R\\YaY RfZkZ RSHSS RVCVX RSHYHYQ R\\S\\[ R\\SaSa[ RfPf[ RdQkQk[ R^D^F RgChLbR RkFiM RYTZYRY',
	    'PobElE RSE^E RRH_H RbIjI RTL]L RTO]O RbOjO RZQ]Q RSZkZ RTJTQ RTJ]J]Q R_R_Y RUSiSiV RbGjGjI RbIbQlPkQ RUSUVhWl[ R[E[H RbCbG RbMeMaM RbKhLjM RdMgM RhMfM RUEUH',
	    'PofPjP Rb[k[ RS[^[ R`[i[ RXQXZ RSS]S]W RcEcM RfQfZ RkCkO RaSkSkW RaWjXl[ RVGXI RSSSW\\X^[ RaSaW R_FSJ R]JTL RZF^I^MTO R^PZP R]DSE',
	    'Po\\FcF ReFlF R[LcL ReNkN ReQkQ RSQYQ R\\RbR ReTkT R\\VbV ReWkW R`[b[ RSHSS RVCVX RSHYHYQ R\\O\\[ R_C_F R\\ObOb[ ReCeIkIkN RfNf[lZl[ R]G]J RbG`L RYTZYRY',
	    'Po^FgF R\\JiJ R\\MiM RZOkO R[QlQ RSW]W RR[][ R`[k[ RTTTW RXRXZ RST]T]W RcIcP RfRfZ RaTkTkW RaTaWjXl[ RRKWKWORQ R`DZG RUDYE RSGWH R`GlI RdGZI RXPaQ',
	    'Po\\DcD ReJkJ R\\NdN RSQZQ R_[b[ RSISR RWCWX RSIZIZQ R\\D\\N R\\QbQb[ R^IaIbL R`Q\\U RjKfO RaU\\Y RiQeU RiUeY RfDeH RfKhMlO RfDiDjHlGiG R_E^HbG_L RdQkQk[g[ RYTZXRX',
	    'PoTElE RYKiK RYMiM RWPlP RXW`W RbWjW RXTXW RYHYM R\\R\\Z RXT_T`W RbTbW RdMeRfZ RYHjHjM RbTjTjW RcZjYj[ RWZ_Y`[ R`CaE R^O[O RTETRS[ RaHaM',
	    'Po\\FlF RfLjL R\\NlN RSQZQ R]QkQ R\\TlT RSHSR RWCWX RSHZHZQ R^LaLaS R^HbHbL RdCdF RgLgS RfHjHjL RdUhYl[ RcT\\X RjUfW R`V`Z R^H^L RfHfL RYTZXRX ReZ][',
	    'PoWHhH RSQ]Q RaQjQlU R_TkT RR[l[ RSNSQ RUVU[ RWEWH RXLXS R\\V\\[ RSN]N]Q R_C_K RaNaQ RbVb[ ReLeS RWEhEhH RRTYUiVi[ RaNjNjQ RTKfJjL',
	    'PoVI[I R`IlI R`UiU Rh[k[ RTRTY RWRWY RZRZX R`D`I R`Q`T RdNdX RhDhI R`QiQiT R`DlDlI RgVjY RUNSS RWM\\M^R RWE[D^J RUFSJ RdDcI`O RaMlMlWk[ RTR]R^XTY RhX`Y',
	    'Po]GbG ReGkG R]JbJ ReJkJ RSQZQ R\\TlT RSHSS RWCWX RSHZHZQ R]DbDcM ReDkDkM R]D]MbMbO ReDeMjMlP RaQ`V RfNdP R^N\\P R^Wk[ RhUdY][ RZTZXRY',
	    'Po\\FlF R]LkL R^OhO RSQZQ R\\RlR R_WjW R_ZjZ RSHSR RWCWX RSHZHZQ R_T_[ RaCaG RaHbLcR RgCgG R_UjUj[ RiP^U RfHfL R]HkHkL R]H]L RYT[XRY',
	    'Po]GlG R^LkL R^NkN RSPZP R^QlQ RfUlU RaZhZ Ri[l[ RSHSR RWCWX RSHZHZP R]S][ R]D]G R_I_Q RaWaZ ReHeQ RfSfU RaWgWgZ RkDlG R]SlSl[ RcS`V R_G_J\\L RdCdG RYTZXRX',
	    'PoUD]D RaDjD RRG^G R`GkG RfKkK RWNhN RS[^[ R`[k[ RUDTG RWLWQXZ RZGZK RSS]S]W RaDaG RWQgQfZ RfGfK RWLhLhQ RaSkSkW RaWjXl[ RSTSW\\X^[ RYDXIVKRL RaSaW RfDdI^KYJ',
	    'Po]GcG ReGkG R]JcJ ReJkJ R]LcL ReLkL R^QkQ RSQZQ R^SkS R^WkW R^ZlZ RSHSS RWCWX RSHZHZQ R]D]L R^O^[ R]DcDcL ReNeZ ReDeL ReDkDkL R_M\\T RYTZYRY',
	    'Po\\FlF R]McM ReMkM R^QlQ RSQZQ R^TkT R^WkW R^ZlZ RSHSS RWCWX RSHZHZQ R]I]M R_P_[ RaCaG R]IcIcM RfOfZ ReIeM RgCgG ReIkIkM RdNgP R_O[T RZT[YRY',
	    'PoSEkE RVIhI RVMhM RYRgR RSX]X RS[^[ Ra[k[ RSKSN RSUTX RXSXZ RSU]U]X R_C_K RfSfZ RXPgPgR RVGVHhGhI RaUkUkX RSKkKkN RaUaXjXl[ RZNSQ RYNYR',
	    'PoSD]D R`DkD RSG^G R`GkG RVNhN RVQhQ RS[^[ R`[k[ RVKVQ RXRXZ RSS]S]W RfRfZ RVKhKhQ RaSkSkW RaSaWjXl[ RSSSW\\X^[ RXH]J ReDeGhIlK RdH`J RYGVJRK RXDWI',
	    'Po\\DbD R[FcF R\\HbH R\\JbJ RcKkK RRKZK R\\PbP RSZjZ RVKVQ R\\M\\P R_P_Z R\\MbMbP RhKhQ RVRiRiV RVRVVhWk[ RXIZK RjIlK RdFgHeK ReMdP RXEVH RUCTFVHSK RTMRP RgCeG RYMYO RjMkP RiEhH',
	    'Po]HcH R^OjO RSPZP R^QjQ R^SlS R^VjV RSHSR RWCWX RSHZHZP R_L_S ReLeS ReDkDkJ R^M[P R_FcF R`WeYk[ R]F_EcDcJ_J RkFhFeEeJgIkIgJ R]D]J`J^P RjWdY[[ RYTZXRX',
	    'PoSK[L_M RUO_O RUQ`Q Rb[k[ RR[\\[ R_[i[ RVJVR RXSXZ R[I[Q RST]T]W RfSfZ RaTjTkW RkOkR RaTaWjXl[ RSTSW\\X^[ R^D`G RgDiF RVESH RgQjR RjJePaQ RZC[GTJ RdCeO RkG`I',
	    'Po_IcI RgKkK R_PlP RSQZQ R`WhW R^ZiZ RSHSS RVCVX RSHZHZQ R_M_P R`T_W RdRdY ReHeL RgMgP R`ThThW R\\DkDkG R_MkMlP R`SlRlZ RcMcP^T R\\GkHgI Rk[i[ RcK_K RYTYXRY R\\D\\S[[',
	    'PoTKjK RTRjR RSW]W RaWkW RT[^[ Ra[k[ RR[Y[ RTUTX RXSXZ RSU]U]X RaUaX RfSfZ RjDjR RaUkUkX RcC`F RYCVF RVQ]P]R RVI]H]I R`QgPhR R`IgHhK ReF_I R[FXFUI RfF_F R[NUN ReN_N',
	    'PoR[l[ RUIU[ R\\I\\[ RcIc[ RUIiIi[ R^D]I',
	    'PoTGkG RUNjN RR[l[ RUGUM RURU[ R\\R\\[ R_C_G RbRb[ RURiRi[ R^O\\T',
	    'Poc[h[ RTHTX RWFWW RZHZW R`V\\[ R_JbPfTlV RcFcQ_Y RTH]H^VRX R^FjFjRh[ RWDWJ RcJbU',
	    'Po^EkE RTHTX RWFWW RZHZW ReIe[ RfKlQ RcL^Q RfEbN RXCWJ RTH]H]VRX',
	    'Po_OlO R][l[ RTHTY RWGWX RZHZW RbTb[ RcNcU RcJcR RdFdO RjKj[ R_EjEjX RXDWJ RTH]H^VRY',
	    'Po`MjM R_SlS RTHTX RWFWW RZHZW ReCe[ RjFlJ RaE_K RXCWK RTH]H]VRX',
	    'PoRMlM RVFVL R\\F\\L R`P`[ RbFbL RVFhFhL R_C^F R`ObTfXlZ RZORR ReMYR RZTWXRZ RhRcT',
	    'PoTHTX RWFWX RZHZW ReJeZ RkOiR RXCWJ RhYdZ RkIdJ RTH]H^VRX RhKjX RkDaFaS_[',
	    'PoaKjK RaQjQ RTHTX RWFWW RZHZW RaEaW R_EkEj[ RXCWJ RTH]H^VRX RlU^W',
	    'PoaZjZ RTHTX RWFWW RZHZW RaRa[ RaRjRj[ RbGdLhPlQ RcC`K RbGiFgKbQ^R RXCWJ RTH]H]VRX',
	    'Po^FlF R_NkN R_QlQ RTHTX RWGWW RYHYW R_I_N RbCbH RcIcN RgIgN RgCgH R_IkIkN RaTcW RhOjP RXCVK RjTfYb[ ReOgY RTH\\H]VRX R_S^Z RlXk[i[',
	    'PoRGlG RUIhI RTKjK RRMlM RSO^O R`OkO RTU]U RaUjU RR[l[ RUWU[ RYOWQ R\\W\\[ R_C_M RbWb[ RfOdQ RUEhEhI RUWiWi[ RgSjS RTQTTXS RTQ]Q]TYS RaQjQjTfS RdSaRaTeS',
	    'Po]EkE R\\LlL Ra[g[ RXLX[ RgLg[ RXCSI RZIRQ',
	    'PocElE RbLlL Rc[h[ RWLW[ RhLh[ R]D_G RXCSI RYJRP R[L_M R`Q[[',
	    'PoZEcE ReElE ReLlL RZNdN Rd[i[ RWLW[ R_E_[ RiLi[ RXCSH RXJRP',
	    'PoeEkE RZEbE RdKlK RYLcL Rf[i[ RVMV[ R`L`X RiKi[ RXDSI RWJVMRQ RdV_W R\\L\\QZX',
	    'PoeElE R[EbE RdKlK R[SbS R[ZbZ Rd[h[ RWKW[ R[E[Z R[LbLbS RiKi[ RWDRH RWIVLRP',
	    'PofElE R\\LcL ReLlL RaWdW Re[i[ RWLW[ R^P^[ RZPdPdW RiLi[ R_CcG RXCSH RXJRP R]GYK R_C\\H',
	    'PoeElE RZHdH RdLlL Re[i[ RWLW[ RiLi[ RZM_R R]U\\Y RXCSH RXJRP R_C_G[O RbM^V R`XYY RbTdY\\X',
	    'PofElE RZJdJ ReLlL Re[i[ RWLW[ R_D_[ RiLi[ RaDdG RXCSH RXJRP RbNdV R[NZX',
	    'PoeElE R\\HcH R[LbL RdLlL RZQcQ Re[i[ RWLW[ R_L_Z RiLi[ RWDRI RWKRP RXIVN R]D[J RcW]Z',
	    'PofElE R\\IbI R]UaU Ra[d[ Rf[i[ RVKV[ RZEZ[ R]M]V R]MaMaU RZEdEd[ RfLkLj[ RWDRH RVLRP RWIUM',
	    'PoeFlF RZJdJ ReMlM RYReR Re[i[ RWLW[ R]C]R RbCbR RiMi[ RXCSH RXJRP RbUdY R\\UZ[',
	    'PoeElE RZGcG ReLlL RYMdM RZRcR Rd[i[ RWLW[ R_C_M R_N_X RiLi[ RXCSH RXJRP RbXYY RdW[Y',
	    'PofFlF R[GcG RZMdM ReMlM R_RcR Re[i[ RWKW[ R[P[Y R_F_X RiMi[ RWCRH RVLRP RWIVM R\\CZJ RdXYY',
	    'PofElE R[EdE ReLlL RYOeO R[XcX Re[i[ RWLW[ R[R[Z R[IcIcO R[RcRcX RiLi[ RXCSH RXJRP R^E\\O',
	    'PogElE RYI_I RaKeK Rc[e[ Rg[j[ RVKV[ RZPZZ RaDeDe[ RgLkLj[ RWDRH RVLRP RWIUN RaDaT_[ RaQeR`R R\\C[P^P^XYX',
	    'PofElE RYJeJ ReMlM R[QdQ RZTdT RXXeX Re[i[ RWKW[ R[L[Q R`Q`[ R[LdLdQ RiMi[ RWCRH RVLRP RWIUM R[T[X R[FcFcJ R]C]J',
	    'PofElE RYHdH R[QcQ R[UcU R[ZcZ Rf[i[ RWKW[ R[M[[ R[McMc[ ReLkLi[ RWCRH RWIVLRP R[D\\G R_H]M RbCaG',
	    'PofElE RYHeH ReLlL RZOdO RZRdR RZUdU R\\YcY RYZaZ RWKW[ RZKZR R_D_Y RZKdKdR RWCRH RWIVLRP RiLi[e[ RcC[E',
	    'PofElE RYIeI ReLlL R[OcO RYReR Rf[i[ RWKW[ RZUZZ R[L[O R_R_[ R[LcLcO RiLi[ RWCRH RVLRP RWIUM RZEcEcI R]C\\I RZUdUdYbY',
	    'PofElE RfLlL RYMdM R[SdS R[WdW R[ZdZ Rf[i[ RWKW[ R[P[[ R_M]P R[PdPdZ RiLi[ RZG]K R]G`K RaGdK RWCRH R\\DZG RWHVLRP R`D]G RdDaG',
	    'PofElE R[HdH ReLlL R]MdM R]PdP R]SdS RZVeV RWKW[ RZLZV R\\C\\H R]K]S R]KdKdS RbWeZ R]WY[ RWCRH RWHVLRP RiLi[f[ RcD[E',
	    'PofElE ReLlL R[NdN R[RdR RYUeU RWKW[ R[H[R R_J_W RZJdJdR R`Wc[ RWCRH RWIVLRP R]CZJ R\\FaF_K R]YY[ RiLi[e[',
	    'PogElE RZG^G R`GeG R`JeJ RfLlL RZM^M R`MeM RZPeP R[SdS R[VdV R[YeY Rf[i[ RVKV[ R[O[[ RZD^D^M R`N`Y R`DeDeM RiLi[ RWDRH R[OXS RULRP RWIUN RZDZMZR RaD`M_S R^JYJ',
	    'PoSHlH RZNZZ R_HXORR RjLgOcQ RbXTZ R_E_H RcPeTk[',
	    'Po[O[[ RZCZI RaM^P RZO`R RSI`IZPWRRT',
	    'PoYNY[ RcCc[ RXN^T RWDYF RhNlQ R[KRT R^N[Q RcLiN RRI\\IZM',
	    'Po^EkE R]NlN RXNX[ ReEe[ RWO[S RVCWF RXMRS R[NYP RRI[HWO',
	    'Po_EkE R^NlN R`[e[ RXNX[ ReEe[ RWO[S RVCWF RYMRS R[NYP RRI[HWO',
	    'PoTFjF RUJiJ RSNkN RZRZZ R_MbTfXl[ R`C_N\\QRU RhRcT RbXUZ',
	    'PoWCWH RXOX[ RXO\\S RbIeM RZLRT R\\NZP R_KcThYl[ RSI[IYN RjJfS`Y[[ R]FkFgQ',
	    'Po`[k[ RXNX[ R`F`Z ReCeU RlWk[ RWO[S RVCWF RXMRS R\\NYQ RRI[HWO RkI]M RkIkShT',
	    'PoXCXH RXNX[ RXO]S R[LRT R]NZQ RSI\\IZM RiDfG_J RjKgO`R RlRhW^[',
	    'Po^JlJ Rc[h[ RXNX[ RhCh[ RWO\\S RVDXF RYMRS R\\NYQ R`NcT RRI[IWP',
	    'Po]IkI R\\PkP RXNX[ RfVl[ RWO[S RVDXF RbU\\[ RYMRS R\\NYQ RdCdPhX RRI[HWP RcP`W RdKbU',
	    'PoRFlF RZUZZ R_C_F RVP^PeOhR R\\MXP R\\IdIfKkM RbMeO R_SdXk[ RiTcW RYIVLRM R_RYURW RaYV[',
	    'Pod[h[ RWCWH RXNX[ RXO\\S R`W\\[ RYMRT R[OYQ RaEhEkM RSI[IWP R`G]M RbN`W R^NjNjUh[',
	    'PoSFkF RRMlM RWQgQ RWJWQ RZTZZ R_C_F RWJgJhQ R`QaTeXl[ R`QTW RjScV R_YV[ RaYXZ',
	    'PoWCWH RXNX[ RXO\\S RYMRT R\\NYP R`E`NdUiYl[ RSI[IWP R_MkMhTcY_[ R_K]X R\\EhEgM',
	    'Poh[l[ RWCWH RXNX[ R^J^[ R^JlJl[ RXO\\S RYMRT R\\NYP R`DfDfMiU RSI[IWP RdMaU ReHgR ReGcR',
	    'PoXNX[ RWO[S RVCWF RXMRS R[NXP RfDjM RSIZHWO RbD^N RcN]Z ReY]Z RgSjZcY',
	    'Pod[k[ RXNX[ R^I^M RdCdZ R^IjIjM RWO[S RVCWF RaV][ RlWl[ RXMRS R[NYP RRIZHWO RcQ_Y RcNaW RcLbV',
	    'Po`OlO R`YlY RXCXH RYNY[ R`E`[ R`ElEl[ RYO]S RSI]I[LRT R]N[Q',
	    'Po^RlR RXNX[ R^I^T ReCe[ R^IlIlT RWO\\S RVDXF RYLRS R\\NYP RRI[HXN',
	    'PoSFkF RVPhP RVJVP RZSZZ RVJhJhP R_OaTeXl[ R`C_P]RSV RjRcU RbYV[',
	    'PoXNX[ RhMh[ RWO[S RVDXF RdDeGkM RYMRT R[NYP RbG\\M RRI[HWP RdCaI RaMaS_X',
	    'Po^NlN RWCWH RXNX[ R^F^Z RXO\\S RYMRT R\\NYP RSI[IWP RlWk[ RjD^F ReY\\Z RfEfOhY',
	    'PoRFlF RUIiI RVMkM RaRdR RVJVL RYUY[ RdIdQ R^C`E RgTdV R_ScWk[ R_SRW R`ZXZ RdL_N[PSQ',
	    'Po\\LlL R\\RlR Rf[i[ RWCWH RXNX[ R_E_[ R_EiEi[ RWO[S RYMRS R[NYP RRI[IWP',
	    'Po]HkH RWNW[ RdCdN RWO[S RVCWF RXMRS R[NYP R_OaSeXl[ RRIZIWP R]NjNhScW[[',
	    'Po]OlO R^ZlZ RXCXH RXOX[ ReFeZ RXO\\S RZLRT R\\NZP RSI\\IYN RkE`F',
	    'PoZHdH RSRlR RZUZZ R_Q`UdXj[ R_CaElJ RWLgLdP RiTbW R^C\\FRJ R_RYURW R`ZV[',
	    'Po`LhL RWCWH RXNX[ RXO\\S ReElL RYMRT RbF\\L R\\NYP RSI[IWP RcCaH R^QiQf[',
	    'Po`NhN Rd[j[ RWCWH RXNX[ RXO[S RYMRT R\\NYP RSI[IWP RbC^L RhS^U R`IlHlRj[',
	    'PoUFiF RSJkJ RVQhQ RVMVQ RZUZZ R_C_J RVMhMhQ R_ReXl[ RiScV R_QXURW RaYU[',
	    'Po]PlP RWCWH RXNX[ R^HjHjP RfVk[ RXO\\S RbU\\[ RYMRT R\\NZQ RdCcQgX RSI[IWP RcLbU',
	    'PoeMiM RRQlQ RVCVN RZUZ[ R`RdWl[ R`Q]SSW RhTcV RcDaL R]KUM RaYXZ R_O`S R_DkDjM RZFRG R\\FUG',
	    'Po]NlN RXNX[ RhWk[ RWN[R RVDXF RbU\\[ RXMRS R[MYP ReQiY RSIZHWO ReFdPaX RdMhW RdKcT RkD^F',
	    'PocJgJ RfQjQ RRRlR RYUY[ RVFVMkMjP R^C\\F R[H`J R^P`TcWkZ RVFgFgJ RhUdW R`RXVRX RaYXZ',
	    'Po]GlG R^LjL RXNX[ RdCd[ RWO[S RWCXF RYLRS R\\NYP RRI[HWP RkXhX R]QlQkX',
	    'Po\\OlO Rf[i[ RWNW[ R_DiDi[ RWO[S RVCWF RXMRS R[NYQ RcHeL RRIZIWP R_D_R^Y',
	    'PobMkM RYM]M RRQlQ RZTZZ RbFbM RbFkFkM RWJSN R_PaUdXk[ RiScV RXDVL R`QYTRV R_N_R RSF^F]M R_ZV[ RaYX[',
	    'Po`[k[ RXNX[ R]G]K RaKa[ R]GlGlK RWO[S RVCWF RcDeF RlWk[ RXMRS R[NYP RRIZHWO RjN`S',
	    'Po_PkP RXNX[ RaUf[ RWO[S RVCWF RdKeN ReFlL RjRdX RXMRS R[NYP RbG]M RRIZHWO RdCaI',
	    'Po\\HiH RSQlQ RXFXO RZUZ[ RYDRK R_PaTeXl[ ReDjE R`QTW RiTcV RbYW[ R`DbKeNiPkPlM',
	    'PoSEkE RWObO RWRiR RXKXQ RZUZ[ R_C_F RWKbKbO RjOiR RYGSL RiTcW R_SbWgYlZ RgOdO R`RYURW RaYY[ RWIhIgO',
	    'Po_RfR RfVjV R_[k[ RWCWH RXNX[ R_L_Z R_LfLfR R^HkHkT RWO[S RlWl[ RXMRT R[NYP RRI[IWP R`C\\L',
	    'Pog[j[ RWNW[ RVOZS RUDWF RVORS RYNXQ ReSb[ RSIYIUQ R_E[N R]S[W RaK]T RaScY RaM[N RbVZW RcIlIlTkZ RgCgMeW',
	    'Po\\IlI R`PkP R^ZlZ RWNW[ RePeZ RWO[S RVCWF R^SZW RXMRS R[NYP RRIZHWP RaJ\\U RcD_R',
	    'Pob[i[ RXNX[ RbIb[ RiWi[ RWO[R RVCWF RdS\\[ RXMRS R[NYP RaDeE RjGcT RRIZHWO RjMlT R^M\\U',
	    'PofNkN R_ZkZ RXNX[ R_Q_[ R_QkQk[ RWO[S RVCWF RYMRS R\\NYP RcJ^N RRI[HWO RdFaL R^ElEkN',
	    'Po_MjM R_UjU R\\ZlZ RWCWH RXOX[ R_E_T R_EjEjT RXO\\S RYMRT R\\NZP RSI[IYN',
	    'Po`LiL R`SiS R\\ZlZ RYNY[ R`D`Z R`DiDiZ RXO\\T RWDYF R[KRT R]NZQ RRI\\HZM',
	    'Po^ElE R^TeT Rc[i[ RXNX[ R^J^U R^JeJeT RiEi[ RWO[S RWCXF RYMRS R\\NYP RRI[HWP',
	    'PoWNW[ R]H]K R]HlHlK RWO[S RVCWF RXMRS R[NXP RcDdH RSIZHWP R`R[[ RaM]Y RgLiW',
	    'Po_QlQ R_ZlZ RWCWH RXNX[ R_I_[ ReCeZ R_IlIl[ RXO\\S RZLRT R]NZQ RSI\\IYN',
	    'PoWCWH RXNX[ RXO\\S RdClK RYMRS RcE\\L R\\NYP RSI[IWP ReKbN^O RgOdR_T RkRfW^[',
	    'Po_HlH R`[k[ RWCWH RXNX[ RaLaZ ReJeW RXO\\S RlWl[ RYMRT R\\NYP RSI[IWP RaC]L RjMeNbO]Q RkLjUgU',
	    'Po`QlQ R`YlY RXCXH RYNY[ R`H`[ R`HlHl[ RYO]S R[KRT R]NZP RSI\\IZM ReCdI',
	    'Po]JkJ RWCWH RXNX[ RdTk[ RXO\\S RfDiG RfU`[ RYMRT R\\NYP RaCaMfW RhNdW RSI[IWP R`Q\\Y RaJbQ RaJ_V',
	    'Po^MlM R\\[h[ RWCWH RXNX[ R^F^W RXO\\S RYMRT R\\NYP RSI[IWP RjD^F RfEfKgV ReV[W RlUkXiX',
	    'Po\\HlH R]OkO RWCWH RXNX[ RdCd[ RXO\\S RgSlX RaS[Y RYMRS R\\NYP RdMfQhU RSI[IWP RdMaS',
	    'PoaJlJ Rd[h[ RWCWH RWNW[ R_G_[ RiCi[ RWO[S RWNRS RZNYQ RbNeT RSIZIWP R`C\\L',
	    'PoSFkF RWRgR RWLWR RZUZZ R_C_F RVMhMgR R[IdIhMlN R^RdXl[ RXJVMRN RiScV R`RXVRW RaYV[',
	    'Po_IkI R]PkP RWCWH RXNX[ RfVl[ RXO\\S RbU][ RYMRT R\\NYP RdCdPhX RSI[IWP R`E]M RdLcU',
	    'PoSFkF RWJiJ RSMkM RZUZZ R_C_F RYGUK R_RdXk[ R_RSX RiTcW R_MbPjR R_M[PSR RaYV[ R_G^N',
	    'Po^NjN R\\SlS Rg[j[ RXNX[ R^H^[ RdCdS R^HjHj[ RWN[R RVCWF RXMRS R[NYP RRIZHWO',
	    'Po_LjL R]SlS RWCWH RXNX[ RdCd[ RXO\\S RYMRT R\\NYP RiElK RSI[IWP R`E^K',
	    'Po\\KlK RcTiT R^ZlZ RWNW[ R^D^Z RcCcT RiDiT RWO[S RVCWF RXMRS R[NYQ RRIZIWP',
	    'PoREkE RZUZZ R_C_F RRMkMgQ R_M_RdXk[ RiTcW R[JdM RUIgI_K R_MXQRR R_RXVRW R_R[R RaYV[',
	    'Po]ElE RaYkY RXNX[ RbLb[ RaOkNk[ RWO[S RVCWF RYLRS R\\NYP RRI[HWO RbJ]U RcE`R',
	    'Po_MkM R_YlY RXNX[ R_Q_Y R_F_M ReCeY RkFkM RlQl[ RWN\\S RVDXF RZJXNRS R\\NZP RRI[HXN',
	    'PoaUgU Rf[j[ RXNX[ RaMaV RaMgMgT RWO\\S RWCXF RYLRS R[NYP RRI[HWO RbC^M R`HlHlTj[',
	    'Po]HlH R_ZkZ R\\ZgZ RWCWH RXNX[ RWO[S R_NdSbV^Z RYMRS R\\NYP RgTjWk[ RhLeR RSI[IWP ReCdH`O',
	    'PodIlI R_ZkZ RXNX[ R_Q_[ RdCdQ R_QkQk[ RWN\\S RWCXF RYLRS R\\NYP RRI[HWO',
	    'Po^IkI R]PlP R\\ZkZ RWCWH RXOX[ RXO\\S RYMRT R\\NYP RiTl[ RSI[IXO RdCdP_Z',
	    'PoWCWH RXNX[ ReCeN RXN\\R RZLRS R\\NYP R`NcUgYl[ R^HlHjL RRI[IYM R^NjNhTcY^[ R^H^R]Y',
	    'PoRFkF RVRhR RVMVR RZUZ[ RVMhMhR R^DaF R`SdXk[ RUK_KeJiK R^SSX RhTcW R^FZHUJ R`YXZ',
	    'PoRGkG RaNkN RRQkQ RZUZ[ RbGbN RkKkN R^O`TcWjZ RbDgE R]D[JXMSN RiTcV RgIbLZM R^RXURW RaYXZ',
	    'Po_IlI Ra[e[ RWCWH RXNX[ ReIe[ RXO\\S RYMRT R\\NYP RSI[IWP RaC]L R`N]V RiNlW',
	    'Po\\JlJ RXNX[ RWO[S RVCWF RhEjG R^SZX RXMRS R[NYP R`QgYlZ RRIZHWO R_E\\J RbJ]U RaOjOgTbY][ RdD`R',
	    'PoRIlI RUNkN RRRlR RZUZZ R\\JaK R_RdXl[ R\\FaG RfObP RiTcW RWDUN R_O_SYURW RWEhEfO RaYV[',
	    'PoaKlK RWCWH RWNW[ R_H_[ RWO[R RWNRS R_HZL RZNYQ RgSkZ RiDkI ReRaZ RSIZIWP RaC^J RfCfNdW RfIhV',
	    'PoRFkF R[IeI R_NjN RYVX[ R_TaVjZ RiUcW R^HTJ RaMWP R_TRX R_YX[ RiOaQVT RdIZMTN',
	    'PobGjG RWNW[ RWOZS RVCWF RWNRS RdC^I R[NYP RRIZIWO RbIeL^P RiHeMaR]T RcTgW_[ RdPlPjTeX\\[',
	    'Po\\GlG R_MgM R]PjP Rc[g[ RWCWH RWNW[ RWO[S RfHkM RXNRS RZOYP RaH\\M RRIZIWP RcC_K RaP_UiTg[',
	    'Po`OlO RaZlZ RWCWH RWNW[ R_H_[ RgFgZ RWOZR RWNRS RYNXP RSIZIWP R`D]L RkEaG',
	    'Po]QjQ R]WkW RWNW[ ReNe[ RVOZR RVCWF RWMRR RbC\\I RYNWP R`GdKlN RRIYIVP R]S]W R`FiFeK\\N',
	    'Po_MhM R^ZjZ RWCWH RXNX[ R^Q^[ R^QjQj[ RXO\\S ReElL RYMRT RcCbF\\L R\\NYP RSI[IWP',
	    'Po\\HlH RaSlS Rc[g[ RWNW[ R_L_[ RgPg[ RWO[S RVCWF RbLkLgP RWNRS R[NYP R_M[Q RRIZHWO RcD^N',
	    'Po]ElE R_RjR R_YjY RXOX[ R_K_[ R_KjKk[ RXO\\S RWDYF RZLRT R\\NZQ RRI[IYM RdEcK',
	    'Po]HlH R^NkN R_ZjZ RWCWH RXNX[ R_R_[ RdCdN R_RjRj[ RXO\\S RYMRT R\\NYP RSI[IWP',
	    'Po]ElE RWNW[ R]K][ RbKbZ RgKgZ RVOZS RUDXF RWORS RZNXP RSIYIVP ReEbL R]KlKl[h[',
	    'Po_ZjZ RWCWH RXNX[ R_Q_[ R]RjRj[ RXO\\S RYMRT R[NYP RaF\\K RaGbKgOlQ RSI[IWP RbC_I RaGiFgJbO\\R',
	    'PofYlY RWCWH RWNW[ RfFf[ RfFlFlZ R^ScX RWOZS RaV\\[ RWNRS RZMYQ RSIZIVP RcP^Y R`C\\T R\\JdJaV',
	    'Po_IkI R]OlO RWCWH RXNX[ RdCd[ RXO\\S RgTlX RbS\\Y RYMRT R\\NYP RdMhU RSI[IWP RdMbS R`E^L',
	    'Po^HkH R]NlN R^SkS R\\ZlZ RWCWH RXNX[ RdCdN RdOdZ RXO\\S RYMRT R\\NYP RSI[IWP',
	    'Po`LjL R^YlY RWCWH RXNX[ R^D^[ R^DlDl[ RXO\\S RYMRT R\\NZP ReGeOiV RSI[IWP RdObV ReKdT',
	    'PoTF`F RRKlK RSP`P RWSWY RZCZK R[S_X RgDjH R_R\\T RZMYPSV R[XTZ RcCcKfYk[mW',
	    'PoSD`D RfOjO RSQlQ RZUZZ RcEcL RkCkO RYDUIXJ[L R_P`TeXl[ RVH^H[LSO RiScV RXGRJ R`QXURV RbYV[',
	    'PodGjG R]MlM R]QlQ RXCXH RXOX[ RdCdL RdQd[ RXO\\S RZLRT R\\NZP RdTkV RSI\\IYN',
	    'PocEkE RcLlL Re[h[ RWCWH RWNW[ R`L`[ RiLi[ RWOZR RaD\\I RWNRS R[NXP RaK[P RSIZIWP',
	    'Po\\HlH R]NkN RRRlR RZUZZ RYCYO RdCdN RTEVG R_RdXl[ RiTcW RWKRM R_O_SXVRW R`ZW[',
	    'Po_SlS R^ZlZ RWCWH RXOX[ ReCeM R_MlMl[ RXO\\S RZLRT R\\NZQ R_EaJ RkEjK RSI[IYN',
	    'Po[ZlZ RWNW[ R]H]Z RbGbZ RfHfZ R]HjHjZ RWOZS RVCWF RWNRS R[NXP RRIZIWO RcCaK',
	    'Po]JjJ R^RkR R\\VlV RWNW[ R\\E\\I RdMd[ R\\ElElI RWO[S RVCWF RXMRS R[NYQ RSIZHWP RbH^Q',
	    'Po^JjJ R^OjO RXMX[ R^E^[ R^EjEjO RWN\\S RVDXF RXMRS R[NYP RbOeVhYlZ RRIZHWO RkReU RdZ]Z',
	    'PoUHgH RUJgJ RUMlM ReSiS RRTlT RUEgEgJ RUEUOjPiR R]C]F RhUcX RYVY[ R_TbXfZl[ RaTRX R\\ZV[ R_ZX[ RaZ[Z',
	    'Po]ElE R_QjQ R\\ZlZ RWCWH RXNX[ R_J_P R_JjJjP RXO\\S RYMRT R\\NYP RiSfY RSI[IWP R`SbY',
	    'Po\\HlH RWCWH RWNW[ R^NaQ ReUk[ RWO[S RhMlQ RbU\\[ RXNRS RZNYQ RcCcQgX RiJfQ RRIZIWP R_K\\R RcP`X RcLdU',
	    'Po_KjK R]YlY RWCWH RXNX[ R]D][ RdGdW R]DlDl[ RWO[R RgQjT RXMRS R[NYQ RdJhS RSI[IWP RdJ`T',
	    'Po^LjL R^UkU R\\ZlZ RWCWH RXNX[ R^E^L R^PdPdZ R^EjEjL RXO\\S RYMRT R\\NYP RSI[IWP RjOaP',
	    'PoSFkF RVKhK RVNhN RTQjQ RRTlT RVHVN R_C_F RVHhHhN R_H_TcXj[ RiVcW RYVYZ R_SYVRX R_YV[',
	    'Po_JkJ R_QlQ R_UlU Rh[l[ RXNX[ R_M_[ R_D_J R_DkDkJ R_MlMl[ RWN\\S RVDXF RYLRS R\\NYP RRI[HWP',
	    'Po_JiJ R_NiN Re[i[ RWCWH RXNX[ R_F_S RdCdG R_FiFi[ RXO[R RYMRT R\\NYP RSI[IXO RlNeV\\Z ReR]S RiRaS',
	    'PoRFkF RUL]L RaLiL RUQ]Q RaQiQ RUIUP RZTZZ R_C_F RaIiIiP R`SdXk[ RiSbV R_QYTRV R]HUI RbYV[',
	    'Po\\IlI R\\PeP RWCWH RWNW[ RcLcX RWOZR RhDkG RgVb[ RWNRS RZNYQ RjNeX RSIZIWP RfCfMfPhY R^L^S]X RlWk[i[',
	    'PoRFlF RbShS RZYdY Rd[i[ RTOT[ RZUZZ RZUdUdY RTOiOi[ RaObS RiIbJ R[RWT R_FXJYM R_E`HcKfMlN R\\HRK R`LVM',
	    'Po_ZiZ RWCWH RXNX[ R_R_[ R_RiRi[ RWO[S RdIlQ RgDkI RcJ\\Q RaE]I RXMRS R[NYQ RSIZIWP',
	    'Po^EkE R`IkI R^NlN RXNX[ RaNa[ RhVlZ RWO[R RVCWF RXMRS R[NYP RjQgT RePiX RRIZHWP RfYa[ ReNgV R^E^Q\\Y',
	    'Po\\GkG R]MiM Rh[l[ RWNW[ RcPcZ RiPi[ RlXl[ RVOZS RVCWF RgIkN R^WZ[ RWNRS R[NXP RbH\\M RcCdG RRIZIVP R^Q^X',
	    'PoRFkF R[O_O RRRlR RZUZZ R_C_O RbKkO RUHZJ R_RdWl[ RhIbK R`R\\SSW RiTcV R_KiN RcDiD R\\LRN RbYV[',
	    'Po]JlJ R^NjN R`ZjZ RWCWH RXNX[ R`Q`[ R^EjEjN R_SjSk[ RXO\\S R`R[V RYMRT R\\NYP RSI[IXN RcH`S RcEaQ',
	    'PoSF]F R`IlI RTPXP RRQlQ RXCXO RZUZ[ RgIgO R_OaTdWjZ RiTcV R`QXURW RaYXZ RjD`E_M R]JSK',
	    'PoSEkE RWJgJ RUOiO RURkR RVLVR RWHWJ RZVZZ R_C_F R_L_O RWHgHgJ RULiLiO R_ScWl[ RiTcW R]TRW R`YU[',
	    'Po]GlG R^PlP R^UlU Rh[k[ RWCWH RXNX[ R^L^[ ReCe[ R^LlLl[ RWO[S RYMRT R[NYP RgDkF RRI[IWP',
	    'Po]HkH RTHZH RSKZK R]NkN RRRlR RTDTH RZUZZ RZCZP RdCdN R_P_SdXl[ RUKSP RiTdW R_RYURW R`YV[',
	    'Po^QiQ Rf[l[ RWCWH RXNX[ R^K^Q RfQf[ R^KiKiQ RlXl[ RWO[S RhGlJ R`W\\[ RXNRS R_G[K RZMYQ RaEgDjI RRIZIWP RaR_Y',
	    'Po_KcK RRRlR RZUZZ RcCcK RgElI RXKSP R^E\\I RRGWH R_P`TdXk[ RiSbV RUDYD RgKbMZO R_RYURV RbYV[',
	    'PoRFlF RVIhI RXOiO RRRlR RZUZZ RVDhDhI RWLiLiO R_O_SdXl[ R]FYLRN R^SSX RiTcW RYKXO R^ZV[ R`YYZ',
	    'Po^JkJ R^OkO R]TkT R\\ZlZ RWCWH RXNX[ R^E^O RdEdZ R^EkEkO RXO\\S RYMRT R\\NYP RSI[IWP',
	    'Po_FlF R`MkM R_SlS RaZlZ RVNV[ RfIfX RUDWF RUOXR R[E]H R[M^M]WZZ RUORS RYNWP RRIXITR RdC`M',
	    'Po_LhL R[ZlZ RWNW[ RVOZR RVCWG RcDlM RWNRS RcCbG[M R[NXQ RjOeZ RRIYHVP R^P_V RcOdU',
	    'Po]FlF R_KkK R`QkQ R^VlV RWNW[ RgMg[ RWN[S RUDWF RdCeF RWMRS RZNXP RSIZHVO RdH`Q R]F]T[[',
	    'PoaPhP RaVhV Rh[l[ RWNW[ R]I][ RaJaV RaJhJhV RcElEl[ RUDWF RWO[S R]D`G RWORS R[NXP RSIZHVP',
	    'PoRFkF RTM[M RTQ[Q RbReR RUIUR RYVX[ RfKfQ R^C`E R]MkMhO RaJgM R_ScWl[ R_HiHbO]Q RhUcW R_SZVRX R_ZXZ R[HTI',
	    'PoWCWH RXNX[ RWO[R RcIgKjN RcJ_N RjEhI RjQhU RXMRT R[NYP ReVhYl[ RSI[IWP R_E^I R_Q^U RdRcV`Y\\[ RdCcK RdPeV',
	    'Po^KkK R^PkP R\\UlU RWCWH RXNX[ R^F^P RcCdFdO RfQf[ R^FkFkP RXO\\S RdP_T RYMRS R\\NYP RSI[IWP',
	    'Po]IjI R]MjM R]ScS Re[l[ RWMW[ R]D]M R]O][ RfOfZ R]DjDjM RlXl[ RVOZR RVCWF RWNRS RZNXP RRIYHVO RkReT RcZ\\Z',
	    'Po_LhL R]PiP R^WhW Rf[j[ RWNW[ R^R^V RcLcV RhRhV RVOZS RUDXF RWORS RZNXP RSIZHVP R_C\\L R`I^N R^HlHlUk[',
	    'Po]KbK RfKlK R]OlO RfRlR RWNW[ R]RbRbZ RbCbK RfCfK RfRfZ RlDl[ RWO[S RUDXF RWNRS R[NXP RSIZHVP R]D]R[[',
	    'Po^GjG R\\KlK RWNW[ RdCdK RWO[S RVCWF RbO\\U RWNRS R[NXP RfMkO R`SeXl[ RRIZIWO R`N\\P R`RiRdW\\[',
	    'PocFkF RcQkQ Rg[l[ RXNX[ RdKdP RcCcI RbSgSg[ RlXl[ RWO[S RVCWF RXMRS R[NYP R]IkIkL RSHZHWO R`Z][ RbT`Z R]I]T\\Z RhMdM_N',
	    'Po_IkI R_MkM R^UlU R^ZlZ RXNX[ R^Q^[ R_E_M R_EkEkM R^QlQl[ RWO\\S RVDXF R\\NYQ RZJXNRS RRI[HXN',
	    'Po`IjI R`MjM RaWiW Rh[l[ RWCWH RXNX[ RaQaX ReFeO RaQiQiV R^ElEl[ RWO[S RYMRT R[NZQ RSI[IWP R^E^R\\Y',
	    'Po\\DbD RVCVH RWNW[ RfVk[ R^UbY RWOZR RhIkM RiHdN R`V[[ RWORS RZMXQ R`J\\N RdQgX R]HbJ R\\T`V ReHiJ RdDkDhK RSIYIVP RbE_M R[QbQ^X RcQkQiVfYb[',
	    'Po]GkG R]KkK R\\OkO RWCWH RXNX[ R`R`Z RgVl[ RXO\\S RYMRT R\\NYP RjSfT RdPhX RSI[IWP ReCdO_S[U RdMfV RfY\\Z',
	    'Po\\ElE Rh[l[ RWCWH RWNW[ R]J][ RdEd[ R]JlJl[ RWO[R RXNRS R[NYP RSIZIWP RaNcV RhNjV RhNfV RaN_V RaN_M',
	    'PoXOgO RSRkR RXJXO R[U[Z RXJgJgO RWDXG RhDeG R_PaUdXk[ RhTcW R_C_G RTGTK R`RYURW RTGkGkK RbYV[',
	    'PoSF\\F RbFkF RTI\\I RbIjI RbMlM RRRlR RYUYZ R\\C\\L RbCbP R_Q`TdXk[ RTM[LYNTP RiTcW R_RYURW RWMRM RaYV[',
	    'PoSFkF RbIkI RTL\\L RbLjL RbOkO RZTZZ R_C_F RbGbQ R`SeXl[ RSI\\H\\NYQUS RiSbU R_RWURW R[NRP RaYV[',
	    'Po\\HbH RfHlH R]NbN RfNlN R\\SbS RfTlT RXNX[ RbCb[ RfCf[ RWO[R RVCWF RYMRS R[NYP RRI[IWO',
	    'Po^IiI R\\MkM RdVgV R_[j[ RVCVH RWNW[ R`Q`[ R_QgQgV RWOZR RfMlR RhDkH R_O[S RjXj[ R^D\\I RWORS RZNXP ReIhP RSIYIVP RbI^Q RcD`O',
	    'Po^IkI R^NkN R\\RlR RWCWH RWOW[ R^D^N RdDd[ R^DkDkN RWO[S RdRlY RdR]Y RWORS R[NYP RSI[IWO',
	    'PoSEkE RVJhJ RVMhM RROlO RVHVM RZVZZ R_C_F RVHhHhM R`H_ScXk[ RZQRT RhTcW RcQkS R_SYVRW RaYV[',
	    'Po\\GlG R^QjQ R^VjV Rd[k[ RWNW[ R_L_W RdJd[ R]MjMjV RlXl[ RWOZS RVCWF RgIlN RWNRS RZOXQ RaI\\N RRIZIWO RcC`K',
	    'PoXE^E RRI`I RVOYO RRRlR RYUX[ RYCYO R\\K^M RfJkN RUJSN R_RdWlZ RaHfJ RaEjEhIeMaN RiTdW R`RYURW R`YXZ',
	    'Po^IkI R^MkM Rf[j[ RWCWH RXNX[ R^DkDkM RXO[R R`P[U RbT\\Y ReWb[ RYMRT R\\NYP RhQdY RSI[IWP RcQaV R^D_M^RlRk[',
	    'PoTF_F RRHaH R]O_O RfPjP RRRlR RTKTO RYCYP RZUZZ RTK_K_O RcDcM RkCkP R_Q`UdXk[ RUCSG RiTcW R_RYVRW RaYV[',
	    'Po]JkJ R]NlN R_ZkZ RWCWH RXNX[ R_T_[ ReJeS R]DkDkJ R_TkTk[ RWO[S RXNRS R[NYP RRIZIWP R]D]Q\\Y',
	    'Po]GkG R\\OlO R^WeW Re[i[ RWNW[ R^R^Y R^ReReW RiOi[ RVOYR RVCWF RWNRS RZNXP RdIkL RRIYIVO RdHaK]M ReCcJ',
	    'Po\\GlG R`LiL R`PiP R\\TlT RWNW[ R`C`T RiCiT RgVl[ RWO[S RVCWF RaV\\[ RWNRS R[NXP RRIZIWO',
	    'PoVGhG RVJhJ RSMlM RVDVJ RYVYZ R_D_Q RVDhDhJ R_ScXk[ RhTcW R]MbOjQ R_MXQRR R_SYVRX RaYW[',
	    'Po\\GdG R[MeM R\\SdS RVCVH RWNW[ R`C`M R`N`Y RhCh[ RVOYR RhLkP RVORT RZNXP RSIYIUQ RdX[Z',
	    'PodFkF R\\JlJ R^RjR R^VjV RWNW[ R^N^[ R_E_J RdCdJ RVO[S RVCWF RXMRS R[NXP RRIZHVO R^NjNj[f[',
	    'Po\\FkF R\\LlL R]PjP R\\SlS RWCWH RWNW[ R]IjIjP RWO[S RXNRS RZNYQ RRIZIWP RdCcP_Wk[ RhSgWeY\\[',
	    'Po]FlF RXNX[ R]L]O R^Q^X RdNd[ RdCeF R]LlLlO RWO[S RVDXF RXMRS R[NYP RaGaK RRIZHWP RhGfL R^QjQjXgX',
	    'Po^HjH R^MjM R\\PlP RdTkT Rc[l[ RWCWH RXNX[ R^D^M RePeZ R^DjDjM RXO\\S RYMRT R\\NYP R_TaY RSI[IWP R_T\\[ R_R]Z',
	    'Po^GlG R^MjM R^PjP RWCWH RWNW[ R^I^P R^IjIjP RWO[S RbP\\V RXNRS RZNYQ R_TcXj[ RRIZIWP R_C]J R_TiTfWbY[[',
	    'Po\\HlH R]OjO R]SjS R]VkV R\\[l[ RWNW[ R]K]S R]EdEdZ R]KjKjS RWO[S RVCWF RXMRS R[NYP RRIZHWO RjDbE',
	    'Po\\FcF R]IcI R\\McM RhPkP R\\TlT RWMW[ R`C`P RdElElN RWOZR RVCWF RWNRS R[NXP RdSeWhYl[ RSIZHVO RcV`Y[[ RdQbX RgEfM',
	    'Po]ElE R^MkM R_UlU Rj[l[ RWCWH RXNX[ R^H^R_[ RcQc[ RhQh[ R^HkHkM R_QlQl[ RWO[S RXMRS RZNYQ RRIZIWP R^M\\Y',
	    'Po[GkG RbLhL R]OjO R[SkS RWNW[ R]D]O RbDbL RcOc[ RhDhL RhXlY RcRiY RVOYR RVDWF RZNXP RVORS RSIYIUQ RcS^XYZ',
	    'Po]IkI R^PkP R^TkT R[WlW RWCWH RWNW[ R\\E\\G R^L^T RdFd[ R^LkLkT R\\DlElG RWO[S RXNRS RZNYQ RRIZIWP',
	    'Po]JkJ R\\MlM RVCVH RWNW[ R]R][ R]D]J RbRb[ RdCdJ RgRg[ RkDkJ RWOZR RWNRS R[NXP RSIZIWP RcMbT R]RlRk[h[',
	    'PoSFkF R^LkL RTM[M RTQ[Q RTITR RZUZZ R_C_F ReIeR RdMkP R_ScXj[ RdLaO]Q RiTbW R^TRW R[HTI RhH`I R^ZV[ RaYX[',
	    'PoSFkF RTL[L R^MjM RTQ\\Q R^QjQ RTITR RZUZZ R^I^Q R_C_F RdGdQ R^IjIjQ R_TcXj[ RiTbW R^TRW R[HTI R`ZW[',
	    'Po^IkI R^MkM RWCWH RWOW[ R^D^M R_Q_XgXgW R^DkDkM RWO[S R`O[T RWORS R[NYP RSI[IWO RfS^U R_QlQlUk[g[',
	    'Po\\KkK R\\OlO RWNW[ RWOZR RUCWF R^VZ[ RWNRS RZNXP R^F^J R\\EcEcI R`SeXl[ RSIZIVP R`Q\\Y RiFgK R`SjSeX^[ RaK_V RjCbD',
	    'PoSEkE R\\KhK RZNkN RWIWR RZUZZ R\\H\\K R_C_F RbKbR R\\HhHiK RXGRM RfOkQ R_RdXk[ R^OYR RiTcW R_RYURW RaYU[',
	    'PobLjL R`PkP RVCVH RWNW[ R_H_[ RbEbL RfLf[ RbEjEjL RVOYR RiUlX RdU`Y RVORT RZNXP RfOiV RfObW RSIYIUQ R`D\\L',
	    'Po\\ElE R_MjM R]UlU R]ZlZ RXNX[ R]P][ R_H_M RdPdZ R_HjHjM R]PlPl[ RWO[S RVCWF RXMRS R\\NYQ RRIZHWO',
	    'Po`KhK R_[b[ RVCVH RWNW[ R\\N\\[ R\\NbNb[ R^QaR R^T`W RWOZR R^EdDkK RWNRS RdD]K RZMYQ RdTg[ RhTk[ RgNdT RkNhT RSIZIVP',
	    'Po^JhJ R\\LlL RVCVH RWNW[ ReTeZ RWOZS RgTlY RkOgS RWNRS R[NXP ReNiW RRIZIWP RbN[Q RbR\\U RbPdTaW[Y Rd[`[ R_C^J R_EhEgL RdQeX',
	    'Po\\DkD R`JjJ R`MjM R_QlQ R\\ZlZ RWNW[ R\\D\\Z R`G`M R`GjGjM RWOZS RVCWF RXMRS RZNXP RSIZHVP RcOaTjW RiReV_X',
	    'Po\\JlJ R_QjQ R^TjT R\\XlX RWCWH RXNX[ R^T^X R_L_Q ReRe[ R_LjLjQ RXO\\S RYMRS R\\NYP RSI[IWP R^FiFiJ RcCaJ',
	    'Po\\GbG R_RjR R_VjV Rf[j[ RWCWH RXNX[ R_O_[ R_OjOj[ RWO[S RXMRS R[NYP RSI[IWP RkFfG RfCfLlKlM RbCbL\\K',
	    'Po\\LlL R`UjU R`ZjZ RWCWH RXNX[ RaQa[ RcCcL R`RjQj[ RWO[R RgNiP RYMRT R\\NYP RjEhI RSI[IWP R^GhH`Q[T',
	    'Po]IkI R\\SlS RWMW[ RdCdO RUDWF RVOYR R_D`G RiDhH RVNRS RZNXP RdHgLlN RSIYHVO RbP`U RdI`L\\N R_WlZ RhTdY\\[',
	    'PoUH[H RTL[L R_KfK R_PkP RXP[P RRRlR RZUZZ RUD[D[O R`H`O R_HfHfK R`E\\H R_RdXl[ RkMkP RiTcW RiMfM R_RXVRW RUDUJTO R_FjFjM R`YU[',
	    'Po]MkM R]RkR R[VlV RWCWH RWNW[ R]I]R RdId[ R]IkIkR RWO[R RXNRS R[MYQ R]D^G RcCcG RkDhI RRIZIWP',
	    'Po^NiN RZZlZ RWCWH RWNW[ R]Q]Z R^D^N RbQbZ RfQfZ R^DjDjN R]QjQjZ RWO[S RcHgL RXNRS RZNYQ RRIZIWP RdFaL',
	    'Po^HkH R^LkL R_[c[ Rh[l[ RXNX[ R^D^L R\\OcOc[ R^DkDkL ReOlOl[ R\\Q_T RWO[S RVCWF RfQiT RXMRS R[NYP RRIZHWO RcT[X RlTdX',
	    'Po]FkF R]IkI R\\LlL R\\SlS R\\WlW Rf[j[ RWCWH RWNW[ R^O^[ RaCaL RdLdV RhChL R^OjOj[ RWO[S RXNRS R[NXP RSIZIWP',
	    'Po\\GlG R`OgO R^ZjZ RWMW[ R^S^[ R`C`I RhChI R^SjSj[ RVOZR RVCWF RWNRS R[NXP RdJgMlO RRIYHVO RcI_N[P',
	    'PoREkE RUKhK RUMhM RcQhQ RUHUM RYUX[ RUHhHhM R`NaRjQjR ReObQ R_ScWkZ RiTdW R_H^N[QTS R^TRW RaYWZ',
	    'Po_ZiZ RWNW[ R\\F\\J R_S_[ R_SiSi[ R\\FkFkJ RVOZR RUCWF RfJjN RcCdF RcM]S RWNRS RZNXP RaJ]N RdLiRlS RSIZIVP',
	    'Po]FkF R\\JlJ R]QjQ RWNW[ R]L]Q R_U_[ RcCcI R]LjLjQ ReUj[ RXPYS RUDWF RVORS RZNXP RdQgX RRIYHUP RbSZW RkSfV RdY]Z',
	    'Po]DkD R`GjG R]JlJ R^TlT Re[h[ RWCWH RWNW[ RaJaP RhQh[ R`VcY RWOZR RjLgN RWNRS RZNYQ ReLhOlQ RSIZIWP RfP_P R]D]Q[Y',
	    'Po[JcJ ReNkN R\\RbR R\\VbV Re[l[ R_[b[ RVCVH RWNW[ R\\N\\[ R\\NbNb[ RfCfN RfPf[ RkKkN RlXl[ RWOZR RWNRS RZMYQ RaFcK RRIZIVP R^D\\J RkFeI RkReU',
	    'PoUG\\G RUJ\\J RRN\\N RRRlR RUDUN RZUZZ RRD^D\\O ReKhLkO RkFjI R_Q`TdXk[ RaF_J RcM_O RiTcW R`RYVRW ReDdL RaYW[',
	    'Po]GkG R]KjK R\\PlP R`SjS R^ZlZ RWCWH RWNW[ RdGdO ReSeZ RWO[S RXNRS R[NXP R`D`G RSIZIWP R_R[Z RhCgF R_P]X',
	    'PohHlH RRJ]J RRSlS RUEUN RYVX[ RXLZN RXGZH RaLjQ R_ScWk[ RbDaH R`JiJfM`P RjUdW RbDhDhH RUE]E]P[P R`SXWRX RaYX[',
	    'PobIkI RbMkM Rb[l[ RWCWH RWNW[ RbDbW RbDkDkM RWOZR R\\I_L RhTlW R]D`F R[P_P_W[Z RWNRS RZMYQ RjPhR RfOiV RSIZIWP RgVaV',
	    'PoeHkH Rh[l[ RWCWH RWNW[ RhTh[ R`KkKkN RlXl[ RWOZR RWNRS RZNYQ R]EeFeRkQkR RSIZIWP RcTbZ R]E\\Q[X R`K`S^Y RiDaE RgNbO',
	    'Po^EhE ReNkP R[TkT RWNW[ RcRc[ RUDVF RVOYR R\\LbL]Q RdLkLfP RVORS RYNXQ R\\MbP RcShXlZ RSIYIUP RcS^XZZ R_FiI RhEbH]I',
	    'PoRFkF RVHhH RVJgJ RVMgM RYVX[ R_H_O RcQeQ R^C`E RUQSS RYQYS R_ScWl[ RVHVOiOhS RdShTcW R_SZVRX R_YX[',
	    'PoURiR RZUZ[ RSMSNkMkP R^N`Q RkEiG RhUdW RUETG R^E\\G RbE`G RXH]J RaSeXl[ ReHlK RdI_K RWIRK RYCXI RfCdI R_SXVRW RaYX[',
	    'Po\\FlF R^KlK RaOhO RgYjY RWNW[ R_S_Y RaHaO ReOe[ RhHhO R_SjSjY RVNZR RUCWF RcDeF RWNRR RZNXP RSIYHVO R\\F\\R[[',
	    'PoUIiI RVLhL RROlO RTFTI R[V[Z R_C_F RdGdP RTFkFkI RVSgRlT R_SdXj[ R[G[PXRRT R_SUX RgUcW RaYW[',
	    'PoSEkE RULiL RTO\\O RbOjO RTR\\R RbRjR RZUZZ R_C_E R_M_R R_TcXj[ RiTbW R[H\\L RbHbL RUHUL RUHiHiL R]TRW RaYV[',
	    'Po\\FlF R_JkJ R_PkP R_TkT R^WlW RWCWH RWNW[ R_M_T RdCdF ReGe[ R_MkMkT RWOZR RWNRS R[NXP RSIZIWP R\\F\\R[Z',
	    'Po`FkF RaMkM RaQkQ R`UlU Rb[l[ RWNW[ RaJaQ RfCfY RaJkJkQ RVOYR RVDWF R\\E_H R\\M^M^X[Z RVORS RZNXP RSIYIUQ R^M[M',
	    'PoeHlH RZ[l[ RWNW[ R\\R\\Z R]E]N RaCaO RaRaZ RfRfZ R\\RjRjZ RUDWF RVOYS RfKjN RVORS RZNWP RSIYITR RfDcL',
	    'Po\\FlF R]OjO R^WhW Rg[k[ RWCWH RXNX[ R\\R\\[ R]H]O RdCdF RjHjO R\\RkRk[ RWO[S RXNRS RZNYQ RgThX RaIgL RRIZIWP RfHcK`L RcOaW',
	    'Po^UlU R^ZlZ RWCWH RWNW[ R^Q^[ R]DcDcM R^QlQl[ ReDlDlM RWO[R RiFfI RjJfM RdNdQ RXNRS R`F]H R[NYP RaJ]M RSIZIWP',
	    'PogGkG R`MlM RfSkS RVCVH RWNW[ R^L^[ RcEcM RcWfZ RWOZR R_D[H RWORS RZMYQ R^K[O RbScY RRIZIVP R_H]N RgCfZk[ RbPaZ',
	    'Po\\HlH R^KjK R]QkQ R\\TlT RWCWH RWNW[ R]M]Q R^E^K RdCdP R^EjEjK R]MkMkQ RWOZR RWNRS RZNYQ RSIZIWP RbQ_Wk[ RhUeY[[',
	    'Po[GaG R`PbP R[SbS RVCVH RWNW[ R^Q^Y RZKaK`P RVOYR RcNhS RVORS RYMXQ RRIYIUQ R^C]K[Q RcJjJkYl[ RbXYY RfCfPdX',
	    'PoTE^E RRH_H RSK_K RTN^N RiPlP RSSlS RYCYH RYKYQ RZUZZ R`FiFjP RlMlP RVIWK RdK`P R\\H\\K RaJfL R_QaUdXj[ RiTcW R_SYVRW ReCcM RaYV[',
	    'PoSFkF RSH]H RRK^K RZN^N RSP]P RZUZZ R_C_F RlOlR RdN`R RWKTN RaMfO R_ScXk[ RhTbV R_SYVRW R`JiJjR RdHdO RXGYKYNZRSS R^RVS R`YW[',
	    'Po\\EkE R]LjL R\\PlP R_VhV RWNW[ R]H]L RaEaL RfEfL R^SiRhV R]HjHkL RWN[R RVCWF RWNRS R[NXP RfPiRlS RRIZHVO RcN^R_ZjYjZ',
	    'PoTG_G RaGlG RWI^I RVK`K RXPZP RRSlS RZVZ[ R[K[P RTD_D_G R^M`P RbJeL RWMVP RhUcW R_ScWgYlZ R`SZURW RiChPeP RaYX[ RTDTJSP',
	    'Po\\ElE R]MkM R^PkP R\\TlT Ra[d[ RWCWH RXNX[ R]H]M RbEbM RdTd[ RfEfM R]HkHkM RXO[R RhVlZ R`V\\Z RYMRS R\\NYP RSI[IWP',
	    'Po\\FlF R]MkM R]QkQ R^WjW R^ZjZ RWNW[ R]I]P R^S^[ RbCbP RfCfP R^SjSj[ R]IkIkP RWO[S RVCWF RWNRS R[NYP RRIZIWO',
	    'Po]EkE R^HjH R\\JlJ R^PjP R^SjS R^WjW RWCWH RXNX[ R^M^W RdCdJ R^MjMjW RXO\\S RYMRT R\\NYP RgXl[ RZJWP RSI[IXN RaX[[',
	    'PoaJkJ RbTkT R[[_[ RVCVH RWNW[ RbNbS RbNkNkS RVOYR RiFkI RVORT RYNXQ RjVl[ RSIYIUQ ReCcJ R[D`D`K\\K\\Q`Q`V_[ RfJgZaZ RkYcZ',
	    'Po\\HjH ReNlN R[NaN RVCVH RWNW[ RiKlM RfEjH RVNRS RWNYOXP RcJhPlR RgIfN RSHYHVP R]I[M R`C_H RcK]QZR RbQ]S RfS]W RiTgWcY\\[',
	    'PoSFkF R_KiK RTL[L R]NkN RTQ[Q RTITR RZUZZ R_H_K R_C_F RdKdR R_HiHiK RbNjQ R_TcXj[ ReN]Q RiTbW R[HTI R^TRW RaYV[',
	    'PoSEkE RaLhL RSNkN RTQjQ RRTlT RVHVL\\LZS RVH]H]K R_C_F RaHaK RdLdS RaHiHiK RiVcW RYVYZ R_TcXgZl[ R]URX R_ZW[ RaYZZ',
	    'Po]HkH R\\PlP R\\SlS R]WkW RWCWH RXNX[ R_L_S RdDd[ R]D]LjLhS R]DkDkL RWO[S RXNRS RZNYQ RRIZIWP',
	    'Po\\IlI R]NkN R]QkQ R[UlU RWCWH RWNW[ RaCaI RfCfI RWOZR RjDiG RWNRS RZMYQ R`J`M R]D^G RdNdUhYl[ RhJgM RSIZIVP RdU`Y[[',
	    'Po\\HcH RfHlH R\\LcL RfLlL RaShS RaWhW Rh[l[ RWNW[ R\\E\\[ RaOaW R\\EcEcL RfEfL RaOhOhW RfElEl[ RVOZS RUDXF RWORS RZNXP RSIYIVP',
	    'Po]HbH ReHkH R]LcL ReLlL R\\QkQ R[VlV RWNW[ R^D^L R`N`U R]DbDcH RfDfL RgNgU ReDkDkH RfXjY RVOZR RUDWF RVORS RZNXP RSIYHUP R`X[[',
	    'Po]HcH RfHlH R]LcL RfLlL RaSgS RaVgV Ri[l[ RWCWH RWNW[ R]E][ R]EcEcL RfEfL RfElEl[ RWOZS RWORT R[NXP RSIZIVP RaOgOgZeZ RaOaV`Z',
	    'Po^GjG R^KjK R[MlM R]QbQ R]TbT RWNW[ R]M]X R^D^K RcMc[ R^DjDjK RgVkZ RVOZR RUDWF RjUd[ RVORS RYNXP ReRhX RdPlPhX RSIYIUQ RdWZX',
	    'PoZGaG RcGlG RVNV[ R[K[R R^C^[ RcKcR RgCg[ RTDVF R]RaV RUOWR RXNWQ RgPkX RgPcX RRIXISR R^PZX RcKjKjQhQ R[KaKaQ_Q',
	    'Po]IcI ReIkI R]PkP R]SkS R[WlW RWCWH RWNW[ R]D]I R]L]S R]DcDcI RdLd[ ReDeI R]LkLkS ReDkDkI RWO[S RXNRS RZNYQ RRIZIWP',
	    'Po^GkG R^JjJ R^MjM R^PkP R\\SlS RWCWH RWNW[ R_F_P ReFe[ RdSkZ RWO[R RdS]Y R_G[J RXNRS RZNYQ RRIZIWP R_C]I ReDdJ',
	    'Po\\JlJ R^ViV R^ZiZ RWNW[ R^R^[ RdDdP RdRdZ R^RjRj[ RVOZR RVCWF RcJ]P RWNRS R[NXP R_F`I RdJiOlP RRIZHVO RhFfJ RiC^E',
	    'PocTjT RVCVH RWNW[ RiLiQkPkQ RVOZS R\\G_I ReFjK RhDfG RjGhI RVORS RaG\\L RZOXQ ReDgH RdUhYl[ RSIYIVP R\\DbD`I RjUfYb[ R`[][ R\\Q[UaU`[ RhLdLcR R]LaLaQ\\Q',
	    'Po^NkN RWNW[ R^H^M R]IkIkM RVOZR RUCWF RWMRS RaE[J RZNXP RhRjX RRIZIVP R`FgEfHdJcMaP\\R RdR\\U RiQeScV\\Y Rd[a[ RdPeVeZ',
	    'Po]GjG R\\McM RdQjQ R[TlT Rc[g[ RWNW[ R_M_Q RdLdQ RgRg[ R\\DjDjJ R\\KgKjLjQ RUDWF R_VaY RVOYS RZNWPRS RSIYIUQ RcP\\R',
	    'PoVCVH RVNV[ R[K[[ R_C_[ RVOXR RVORS RYNWQ RgRk[ RbEcI RiRe[ RSIXITR R\\EZJ RaOaU RfIhW R]O\\U RfDdN RfIkIhV R[KcKc[a[',
	    'Po^FjF R\\IkI R\\MbM RdMkM RdQlQ R[TlT Rf[l[ RWNW[ R_K_Q RdCdI RfTf[ RhKhQ RlXl[ RVOYR RVDWF RVORS RZNXP RSIYHVP RaT_Y[[ RbP[Q',
	    'Po\\FlF R`JhJ Rh[k[ RXNX[ R]M][ R`C`J RdJd[ RhChJ R]MkMk[ RWO[S RVCWF RXMRS R[NYP R_PcW RfPiW RgQfW RSIZHWO R`R_W',
	    'Po`JjJ R`SiS Rh[l[ RWNW[ R]I][ R`M`S ReHeZ R`MiMjS RbElEl[ RVOZS RUDWF ReRiW R\\D_G RdR_X RVORS RZNXP RbNcS RgNgR RSIYHUP',
	    'Po_LhL R\\TlT RWCWH RWNW[ R]F]R RbCaF R]FjFjQ RWN[R RdL_P RgIfK RWNRS R[MYP R`HeHdOhO RdSeVhYl[ RRHZHWO RdSbW`Y[[ RaJaL',
	    'Po]IkI R]LkL R[PlP R\\TlT R]WkW RWCWH RWNW[ R]D]I RaDaI RdIdO RdTd[ RfEfI R]DkDkI RWOZR RWNRS RZMYQ R`Q`T RhQgT RSIZIVP',
	    'Po]QjQ R^WiW R^ZiZ RWCWH RWNW[ R]K]Q R^S^[ RdKdQ R^SiSi[ R]KjKjQ RWN[R RdCiH RWNRS R[NXP R`M`O R`IgHlI RgMgO RRHZHVO RcC_H[I',
	    'Po_IhI R\\PbP RePkP R[SkS RWNW[ R\\K\\P R_D_I R\\KbKbP RcQc[ ReKeP R_DhDhI ReKkKkP RXPYS RcSiXlY R_W[Y RcS]Y RVORS RZNXP RVCWF RRIYHVP',
	    'PobIiI Rb[l[ RWCWH RWNW[ RhOhW RWOZR R\\I^K RfMlT R]D_F RfIbM R[P_P^W[[ RWNRS RZNYQ RiDkI RSIZIWP RbE`I ReO`Q RkLgPdT`U RfMhT RgXcX ReDiD',
	    'Po]IkI R]LkL R\\OlO R_QjQ R\\UlU RVCVH RWNW[ R]F]L R`T`Z RbCbL RfCfL R]FkFkL RWOZS RWNRS RZNXP RcTgYl[ RSIZIVP RjVfX ReY]Z R\\O\\T[Z',
	    'Po\\IlI RgLlL RaNiN R]PlP R`RjR R`UjU R`ZjZ RWCWH RXNX[ R`W`[ RfHgL R`WjWj[ RWO[R RXNRS R[MYQ R`EgEdH RRIZIWP RcHbK_L R`E\\I]R\\Y',
	    'Po`JhJ R]SbS ReSjS RWNW[ R]M]S R]MbMbS ReMeS ReMjMjS RhWl[ R_VbY RWO[S RVCWF RXMRS RdC^J R[NYQ RdDhHlI RgTeZ RRIZHWO R_U]Z',
	    'Po`EkE RTH]H R_IlI R`MlM RUP]P RRRlR RTDULUO RZUZZ RTD]D]H ReIeP RUK]K^P_SdXk[ RhFhH RiTcW R_RYVRW RTGSO RbYV[',
	    'Po\\FcF ReFlF R^OjO R\\SlS R`[d[ RWCWH RWNW[ R`D`N RdSd[ RhDhN RWOZR RhVlZ R_HcK R_V[Z RWNRS R_I[L RZNYQ RhEjK RSIZIWP R`E^J RhEeK',
	    'Po`PiP R^VkV R^ZkZ RWCWH RXNX[ R]H]K R^R^[ R`K`O RdCdH RdRdZ R`KjKjO R^RkRk[ R]HlHlK RWO[S R_D`G RjDiG RXNRS R[NYP RRIZIWP',
	    'Po\\JlJ R]ThT Rg[j[ RWCWH RWNW[ R\\D\\I R]P]T RbDbI RbNbX RgDgI R]PhPhT R\\DlDlI R\\YbYgWhY RWOZS RWNRS RZNXP R_L[P RSIZIWP R_J^NlMlTk[',
	    'Po\\FlF R`MgM R]OjO R^UjU R^XjX R[ZlZ RWCWH RWNW[ R]H]O R^R^X R`JaM RdCdF R`JgJgM R^RjRjX R]HjHjO RWO[S RXNRS RZNYQ RRIZIWP',
	    'Po^GiG R^JiJ R^LiL R]QkQ R]SkS R[VlV RVCVH RWNW[ R]N]S R^E^L RdNdS RgVg[ R^EjEjL R]NkNkS RWOZS RWNRS RZNXP RSIZIVP R`V_Y[[',
	    'Po\\DdD RfHlH R\\KcK RfNlN R\\OdO R[ZlZ RWCWH RWNW[ R\\D\\O R]R]Z R`D`G RbRbZ R\\HcHcK RfRfZ R]RjRjZ RWOZR RWNRS R[NXP RSIZIWP R`K`O RgDeL',
	    'Po^IlI R_NkN R`QjQ R^UlU RWNW[ R]F][ RcCcI RgCgI ReNeUfWjZ RVOYR RVDWF RaKbN R_E`H RjEiH RhJgN RdV`Z RVORS RYNXQ RSIYHUQ R]DZM R]C\\K',
	    'Po]EkE R^JbJ RfJjJ R]MbM RfMjM R\\PlP Ri[k[ RWCWH RWNW[ R\\H\\K R]T][ RbSb[ RdEdN RfTf[ R]TkTl[ R\\HlHlK RWO[R RXNRS R[NYP RSIZIWP RbPbV',
	    'Po\\DlD Rh[k[ RWNW[ R]I][ RdDd[ R\\KkJk[ R_TbW RVOYR RfTiX RVDWF RfMiQ RgFlJ R_MaO^Q RhLgP RaS`X RhSfX RVORS R`F[J RZNXP RSIYIUQ',
	    'PoeIlI R[JdJ R[OdO R\\TcT Rg[j[ RWMW[ R]K]N R_D_J R`O`Y RaDaJ RjDj[ RXPYS RVORS RZNXP RVCWF R[E\\H RRIYIVP RcEcH RbKaO RfOgU RcX[Y',
	    'Po]ElE R^HkH R]NkN R^SkS R^UkU R^WkW RXNX[ R]J]N R^P^W RdCdH RgJgN R^PkPkW R]JkJkN RWO[S RWCXF RXMRS R[NYP RgXl[ RSIZHWO RaY[Z RaJbN',
	    'Po\\ElE R]QlQ RWCWH RWNW[ RaCaH RbIbN RfIfN RgCgH R]IkIkN RlXl[ RfXi[ RWO[R RXNRS RZNYQ RdSgZ R]I]NiNjP R]TbW RRIZIWP RiSeX`Z RdNeX R]Q\\Z',
	    'Po[GkG R[JaJ ReJkJ RfQkQ R\\Q`Q R\\T`T RfTkT RVCVH RWNW[ R\\M\\T RfMfT RhJgMkMkT RWOZR RWORS RZMXQ RcCcSeVhYl[ RRIZIVP RcRaW R^J]M`MaU_YZ[ RcOdV',
	    'Po[IkI R[NcN R\\SbS R\\VbV Re[l[ R_[b[ RWNW[ R[D[H R\\P\\[ R\\PbPb[ ReSe[ R[DkDlH ReJeQkPkQ RlXl[ RUDWF RVOYS RaKbN RVORS RYNXQ R_J[N RSIYIUQ RkTeV R`DaI RfDfI RkLeM',
	    'PocElE R[GbG RdMkM R\\MbM RdQkQ RdVkV R\\XbX RVCVH RWNW[ R\\Q\\Z R_C_M R\\QbQbX RdIdV RgEeIkIkV RWOYR RiXlZ RfXb[ RWORT RZNXP RSIYIUQ',
	    'Po]GjG R]JjJ R\\MkM R[QlQ R`[c[ RVCVH RWNW[ R]D]J RdSd[ R]DjDjJ R_TaV RVOZS RaJaQ]U RgJgQiTfV RWNRS RZNXP RfXkZ RSIZIVP RaX\\Y',
	    'Po[FcF ReIkI R[McM ReNkN R[QcQ Ri[l[ RVCVH RWNW[ R]H]L R_C_G R_M_[ RaHaL ReDeSjSi[ ReDkDkS RlXl[ R_RbV RVOZR RVORS RZMXQ RSIYIVQ R_P\\W RdZb[ RfSdZ',
	    'Po[GbG RdPkP R[QbQ RdSkS RdWkW RVCVH RWNW[ R[J[P R_C_[ R[JbJbP RdLdW RdLkLkW RiXl[ R^QbU RVOZR ReXb[ RVORS RYMXQ RSIYIUQ R^OZX RgDdJ RkJhJ RdDkDkJ',
	    'PoeFlF R[FcF RZLcL ReNkN ReQkQ R\\RbR ReTkT R\\VbV ReWkW Re[l[ R_[b[ RWNW[ R\\N\\[ R]H\\K R_C_F R\\NbNb[ ReNe[ ReCeIkIkN RVOYR RVDWF RZNWPRS RSIYIUQ RaH`L',
	    'PoaEkE RSE^E RRH_H RaIjI RTM]M RaOjO RTO]O RZQ]Q RRTlT RTJTQ RTJ]J]Q RaGjGjI RbIbQkPkQ R[E[H RhUcW RYVY[ RaCbG R_TbXgZl[ RaTRX RgKjK RaKeKgK RgMiM RaMeMgM RhKfK RhMfM RUEUH R^ZW[ RaZY[',
	    'Po[KlK RbNeN RbReR RVCVH RWNW[ R_K_Y RVOYR R]F_H RbFeI RiEkH RVORS RZNXP RlWk[ RSIYIUQ R^C[J RcD`I R\\N_N[N R\\R_R[R R[U_U[U ReUbU RjNgShZ RbKcX[Y RgCgNhRgWdY]Y',
	    'Po\\HcH RfHlH R\\KcK RfKlK R_NjN R`UiU Rh[l[ RWCWH RWNW[ R\\D\\[ R\\DcDcK RdMdZ RfDfK RfDlDl[ RdTiY RWOZS RdT_Y RWORT R[NXP RSIZIVP R`QiQiU R`Q`U',
	    'Po\\DkD R_GhG R_JhJ R\\MjM R]RbR ReRjR R]UbU ReUjU RdYjY RVCVH RWNW[ R]O]Y R_D_L R[ObOb[ ReOeY RhDhN RdOkOk[ RWOZS RWNRS RZNXQ RSIZIVQ RbX[Y',
	    'Po\\FdF RZIlI R\\OdO R[TeT RZWeW RWNW[ R^Q^W R`C`I R`L`Q R\\L\\QcQbW R\\LdLdQ RbYdY RVOYR RVDWF RiEkG R^YZ[ RVORS RZNXP RkQf[ RlWk[ RSIYIUQ RkMhX RgCgNgPiZ',
	    'PogGjG R]J`J R[NlN R^TiT R\\WkW R`[c[ RVCVH RWNW[ R]D]M RcQc[ RbCbGeHeM RjDjM RWOYR RgOlS R`N[T RWORT RYNXQ RSIYIUQ R`D]D RbJbN RbEeEbE R`G]G RgJjJgJ RgDkD RfP_Q',
	    'Po\\FbF ReFkF RdIlI R]QjQ R]TjT R]WjW RWNW[ R]O]W R]OjOjW RVOYR RVDWF RVORS RYNXQ RfXl[ RSIYHUQ R]D\\G ReDeG RhCgIeM R\\IaIaM_M RaX[[ R_C^I\\M RlMiM RhFiL',
	    'Po[DbD RdDlD R[MlM R[SkS R^VbV RVNV[ R[G[K R^S^[ R`M`S R[GaGaJ ReGkGkJ R[PkPkS RfMf[lZl[ RUDWF RUOXR ReGdJcM RUORR RYOWP RhHhJ RRIXITQ R_H^J RkVeV RbZ]Z R[M[SZZ',
	    'PoZFlF ReTiT Ri[k[ RVCVH RWNW[ R[J[[ R\\V\\Y R\\TaT`Z R_C_I RaUaY RcHc[ ReOeT RgCgI ReOjOjT RiVjZ R[JkJk[ R\\O^Q RVOYR RVORS RYNXQ R^L]O RSIYIUQ R`N^R RgLhXdY',
	    'Po[OlO R^UhU R\\XjX RWCWH RWNW[ R^D^M RiCiM RWOZR RfOkU ReIaM[U RWNRS RZNYQ ReDaH RfFjFlK RaJeL RaEeG RSIZIWP R[F_F[L RiEfK RcRc[_[ RfR_R',
	    'Po[EkE R\\IbI R\\ObO R]SiS R[VkV R[ZjZ RWCWH RWNW[ R_H_K R_N_R R`C`G R[KaKaM RdJjJkQ RhXk[ RWOZR RaV]Z RWNRS RZMXQ R]L\\N RdMiO RSIZIVP RgCgKfP RbQ[R',
	    'PoZElE R[KaK ReKkK R[NaN ReNkN R^PhP R_VhV Rh[k[ RVCVH RVNV[ R[I[[ R_R_V R_C_G R[IaIaN RcOcZ ReIeN RgCgG R_RhRhV ReIkIk[ RcUgY RVOYR RcU_Y RVORS RYNXQ RSIYITR RaR`U ReReV',
	    'PoRHlH R[XcX RSOS[ R[H[Z RcHcZ RSOkOk[',
	    'PoRElE RSYkY RSKS[ RcEcShSkS RSKkKk[ R[E[PWT',
	    'PoRGlG RSXjX RSNS[ R[G[Y RcGcY RSNkNk[',
	    'PoSEkE RTNjN RRRlR RTITN R[E[N RcEcN RTIjIjN RfRbW^YR[ R\\PVWjZ',
	    'PoRDlD RTMjM R_OeO R[[l[ RTHTL R[D[L RcDcL RTHjHjL RTP^P`S RWXRZ RTTiT`WXX RYZa[ RhNaO',
	    'PoRElE R[KcK RVQhQ RVThT RRWlW RTHTL RVNVT R[E[K R_T_[ RcEcK RVNhNhT RTHjHjL',
	    'PoRDlD RTLjL RZQlQ RTHTK RWPW[ R[D[K RcDcK RTHjHjK ReNiO RYNRT RbPdVhYl[ RaT]YY[ RbMaU',
	    'PoRE_E RSN^N RRR_R Re[i[ RSHSN RWEWN RZEZN RSH^H^N ReUaY RTV]Z RhNcX RXOUV RcE`M RfJaT R\\R[VXYS[ RiIeU RbIlIlNlQj[',
	    'PoSEkE RTJjJ R]NkN R\\QiQ R\\TiT RTGTK RWQW[ R[E[J R\\N\\T RcEcJ R[OiOiT RTGjGjK RWLSP RXORU R^LYQ R`UYX R]VhWaYl[ ReYZ[',
	    'PoRElE RTKjK RRN_N RUQ\\Q RbQkQ RbVkV RSV^V RRX_X Rf[j[ RSSSV RTGTK RULUQ RYQY[ R[E[K R\\L\\Q RSS^S^V RcEcK RTGjGjK RbNkNk[ RbNbTaY',
	    'PoRElE R[JcJ RTP]P RTR]R RRU_U RX[\\[ RTMTR RTGTK R[E[J RVX]W\\[ RTM]M]R RcEcJ RTGjGjK RbOeViYl[ RcK`S RXRXUUZR[ RbOkOgUcY_[',
	    'PoRElE R[IcI RRM_M RaMkM RUP\\P RSU^U RRX_X RaXfX RSRTU RTGTJ RUKUO RXPX[ R[E[I R\\K\\O RSR^R^U RaTaY RcEcI RaTfTfX RTGjGjK ReNjP ReKdOaP R_RkRi[f[',
	    'PoRElE R[JcJ RaMkM RRN_N RaPkP RUQ\\Q RaRkR RSV^V RRX_X Rf[j[ RSSSV RTGTK RULUP RXQX[ R[E[J R\\L\\P RSS^S^V RcEcJ RcWcZ RfMfU RTGjGjK RhWiX R`W_[ ReWfY RaMaUkUj[',
	    'PoVIhI RVNhN RVShS Rb[k[ RVDVS RcSc[ RVDhDhS RlWk[ R[SYWWYR[',
	    'Po^IjI R^NjN R^RjR Rf[l[ R^E^R RgRg[ R^EjEjR RlXl[ RaW][ RWTRY RSJVM[W RZMUW R[HXT RbR`Y RRF[FYR',
	    'PoWNhN RWQhQ RWUhU Rb[k[ RWKWT R_C_J RcUcZ RWKhKhT ReEhFkJ RlWl[ RYETIRJ R[VXYR[',
	    'PoTI]I R`MlM RTN]N R[[k[ RTETR\\S[Z RTE]E]R RkWkZ RTZR[ RWSUY RaEkEfJfWbW',
	    'PoSH\\H R_IjI R_NjN RRO]O R_RjR Rg[l[ R_D_R RgRg[ R_DjDjR RlWl[ RWDWNVTSZ RWR[V RbS`W][',
	    'PoTI]I RTM]M R[[l[ RTDTQ\\Q[Z RTD]D]Q RcQiW RdJ_K RiDbK RbMeT R`EjJ RTZR[ RiMfRbV^W RVQUY',
	    'Po_IjI RTK]K R_NjN R_RjR Rg[l[ RZCZK RTQ[Q[[ R_D_R RgRg[ R_DjDjR RlXl[ RaW][ RbR`Y RTDTTR[',
	    'Po_IjI R_NjN R_RjR Rg[l[ RWFWY R_D_R RgRg[ R_DjDjR RlXl[ RaW][ R\\XVY RbR`Y R[DRG R\\KRL R\\QRR',
	    'PoXEaE RWNhN RWQhQ RWUhU Rb[l[ RWJWU RcUc[ RWJhJhU RfFkK RlWk[ RWGRK R^F_I R[VXZR[ RgD_D',
	    'PoSDkD RVOhO RVRhR RVVhV Rb[k[ RVLVV R`F`K RbVb[ RVLhLhV RlWk[ RaGlJ RbDYIRK RZYR[',
	    'PoaIkI RaNkN RaRkR RWY]Y Rg[l[ RXHXY RaDaR RhRh[ RaDkDkR RlXl[ RWD[G RXRRX R]HWT R]U]Y R]L_R RTLSS R]EZO RdRbY',
	    'Po_IjI R_NjN R_SjS Rf[l[ RXCXH RXNX[ R_E_S RgSg[ R_EjEjS RlXl[ RXN\\R RXNRS RSI\\IXN RbS`X][',
	    'PoRIZI R`IkI R`NkN R`SkS RSTYT RX[\\[ Rg[l[ RSMSV RSMYMYT RRE]E]Z R`D`S RhSh[ R`DkDkS RlWl[ RaZ^[ RcSaY',
	    'PoVI]I R_IjI R_NjN R_RjR RRX[X Rg[l[ RRORZ RVCVO RRO[O[X R_D_R RgRg[ R_DjDjR RlWl[ RaW][ RbR`Y',
	    'PoaIjI RaNjN RaRjR Rg[l[ RU[X[ RYHY[ RaDaR RhRh[ RaDjDjR RlXl[ RcW_[ RVDRM RUH^H\\L R[N]W RUNSW RdRbY',
	    'PoWNgN RWRgR RWUgU Rb[l[ RSHSL RWKWU RcUc[ RWKgKgU RSHkHkL RVDXF R^C_F RlXl[ RhDeH R[VYYS[',
	    'PoaIkI RaNkN Rh[l[ RXJXY RaDaRjRh[ RaDkDkR RlWlZ R_N\\Q RcW_[ RdRbY R^HXJ R\\WVY R[I]V R^DTFTQSZ',
	    'PobIkI RbNkN Rh[l[ R\\D[X_W_X RbDbRjRiZ RbDkDkR RlWl[ R\\O`S RWORS RSHUL R_H]L RbZ_[ RdRcY RWDWQVW',
	    'PoSE]E R`IjI RRJ^J R`NjN R`RjR Rg[l[ RXEXN R`E`R RgRg[ R`EjEjR RlWl[ RXU\\Y RXVSZ RbW^[ RcS`Z RTQRU R]Q[T RXOWW',
	    'PoTI\\I R_IjI RTN\\N R_NjN R_RjR Rg[l[ RTETR[RZY RTE]E]R R_E_R RgRg[ R_EjEjR RlWl[ RaW][ RbRaY RTZRZ R]WYY RVRTY',
	    'PoSH_H RaIkI RSM^M RaNkN RaRkR RWZ\\Z Rg[l[ RWRWZ RYCYM R\\W\\Z RaDaR RhRh[ RaDkDkR RlWl[ RXOZR RbW_[ R]S_W RTSRX RcRaY',
	    'PoRI_I RaIkI RTM]M RaNkN RaRkR RVY^Y Rg[l[ RVPV[ RTE]E]M RUR^R^Y RaDaR RhRhZ RaDkDkR RlWl[ RcW_[ RWMTU RdRbY RYEVS',
	    'PoRE^E R`IkI R`NkN R`RkR Rg[l[ RXEXX R`D`R RhRh[ R`DkDkR RlWl[ RTMWR R\\M^R RaY][ RcSaY RUIST R\\IZT R_VRX',
	    'Po`IjI R`NjN R`RjR Rg[l[ R`D`R RhRh[ R`DjDjR RlXl[ RXT]Y RWVS[ R^Q[T RcW_[ RXJTN R]E\\I RXJ]L RUFTI RUQSU RdRbY RYCWK RYOWX',
	    'PoRI^I RaIjI RaNjN RaSjS Rg[l[ RYEYN RaDaS RhSh[ RaDjDjS RlWl[ R[J^N RcW_[ RYHSN RWI[K RTV]Z RWOTV RRR]R[VXYS[ RcSbY R]DSE',
	    'PoYF^F RbIjI RbMjM RYP^P Rh[l[ RYCYI RYJYP RXS[S[Y RTI^I^K RbDbRjRiZ RbDjDkR RlXl[ R_W[Y RbZ`[ RWSVY RdRcY RTITSRZ R\\LVM',
	    'PobIkI RTM_M RbNkN RRT`T Rh[l[ RTGTT RWCWT R[C[T RTG_G_T RbDbSiSh[ RbDkDkS RlXl[ R[V^Y RWVRZ RbZ`[ RdScY',
	    'PobIkI RVJ]J RbNkN Rh[l[ RV[X[ RSNS[ RSNXNX[ RbDbSjSi[ RbDkDkS RlWl[ RUQWR RUUWW RTEZD`J RZDSJ RZT][ R]S`Y R\\MZT R_N]T ReScY',
	    'PoSD^D RbGlG RSJ]J RbKkK RSL_L RVQgQ RVTgT RVVgV Rb[k[ RSDSL RVNVV RXDXG RXJXM RSG]G]J RbWb[ RVNgNhV RkXk[ RcC`I R\\VYYR[',
	    'PoaIjI RYJ]J RSM]M RaNjN RRQ^Q Rg[l[ RW[[[ RSDSJ RYDYJ RYD^D^J RaDaRiRh[ RaDjDjR RlWl[ RaY][ RcSbY RUQUU\\T[[ RSDWDWJSJ',
	    'PoSG[G RaIkI RRL_L RaNkN RaRkR RUU]U RUY]Y Rg[l[ RVOV[ RXCXK RTQ]Q]Y RaDaR RhRh[ RaDkDkR RlWl[ RZN]N RbW_[ R\\ITQ R^E[J RcSaY',
	    'PoSF^F RaIkI RRM^M RaNkN RRQ^Q RaSkS Rg[l[ RYCYG RYMY[ RaDaS RhSh[ RaDkDkS RlWl[ RYR\\U RYQRX RUHVL R\\H[L RcSaX_[',
	    'PoSF`F RbIjI RbMjM RSP`P Rh[l[ RY[][ RSKSN RYMZP RSK_K`N RbDbRjRiZ RbDjDkR RlWlZ RYCYF RdW`[ RVGWJ R\\G[K RTZRZ ReRcY RWPVX RWT^T][',
	    'PoSI^I R`IkI RRK_K R`NkN R`RkR RTS^S Rg[l[ RSDSH RTNTS RUTVX RYCYH RTN^N^S R^D^H R`D`R RhRh[ R`DkDkR RlWl[ RaY][ RcRaY R[T\\XTY',
	    'PobIkI RUL]L RbNkN RUP]P RST_T Rh[l[ RSDSG RUHUP RUH]H]P RSD_D_G RbDbRjRh[ RbDkDkR RlWl[ RXRZT R[V_Y RWVRZ RbZ`[ RdRcZ',
	    'PoXQgQ RXTgT RXVgV Rb[l[ RSLSO RXNXV RbWb[ RXNgNgV RSLkLkO RXH]J RjEhG RlXl[ RVESG R]E[G RbE_G RdHkK ReDdI_J RWIRK RYCXI R\\VYYS[',
	    'PoSF`F RSI_I RbIkI RRL`L RbNkN RTS^S RRV`V Rh[l[ RZ[^[ RTPT[ RVCVL RYLYV R\\C\\L RTP^P^[ RbDbRjRh[ RbDkDkR RlWl[ RbZ`[ RdRcY',
	    'PoYE_E RbIkI RbNkN RYN_N RWS^S RWV^V Rh[l[ RWPWZ RYIYN RYCYH RTH`H_K RbDbSiSh[ RbDkDkS RlWl[ RbZ_[ RdSbY RTHTTRZ R]KUL RWP^P_YWZ',
	    'PoRE_E RbHlH RSK^K RRM_M RVRhR RVUhU RVWhW Rb[k[ RVPVW RYCYO RSG^G^K RVPhPhW RhHhN RkXk[ RbWb[ RZI^I RSGTJWISI RVIYIUI RYZR[ RbGaN RjDbE',
	    'PoRF_F RUI\\I RaIkI RaNkN RSP^P RaSkS RSS^S RSV^V RYY_Y Rh[l[ RUCUI R\\C\\I RSL^L^P RaDaS RhSh[ RaDkDkS RlWl[ RbY^[ RSLSP RcSbY RYIYYRZ R[YUZ',
	    'PoTG^G RaIkI RRJ_J RaNkN RTO^O RUR^R RaSkS RUU^U RUY_Y Rg[l[ RUMUZ RZMZY RaDaR RhSh[ RaDkDkR RlWl[ RUCSI RXGSO RcSbW_[',
	    'PoaIkI RVK\\K RaNkN RUS^S Rh[l[ RUNUS RUN^N^S RaDaRiRh[ RaDkDkR RlWl[ RSDYD_J RcW_[ RTGVHRL R]D[F R_F]H RVUVX RXDVI R\\UZY RdRbY R_XSZ',
	    'PoRHWH RZH_H RbIkI RRLWL RZL_L RbMkM R\\[_[ Rh[l[ RRER[ RREWEWL RZEZL RZE_E_[ RbDbRjRi[ RbDkDkR RlXl[ RbZ`[ RVO[O[YXY RdRcY RVOVUUZ R[RVR R[UVU',
	    'PobIkI RTM^M RbNkN RUR]R Rh[l[ R][_[ RTOT[ RTJ^J^M RTO_O_[ RbDbRjRi[ RbDkDkR RlWl[ RWWZX R\\F_I RVU[UWY RXFXH RTERJ RYMWR RbZ`[ RdRcY R]DTE',
	    'PoYE_E RcIkI RcMkM RYN_N RVUYU Rh[l[ RVRVU RYCYH RZIZN RYPYY R\\P\\Y RTH`H`K RcDcRjRhZ RcDkDkR RlXl[ R_R_U\\V RdRcY RTHTTRZ R]KVL R`XUZ',
	    'PoRHWH RYH^H RaIjI RRLWL RYL^L RaMjM RVSZS RVWZW R[[^[ Rh[l[ RRDR[ RVOVW RRDWDWL RYDYL RVOZOZW RYD^D^[ RaDaRiRh[ RaDjDjR RlXl[ RbZ_[ RdRbZ',
	    'PoSIYI R_KdK RSNYN R_NdN RSEYEYR R]K][ R_E_K RbCbK R`QdQdX RdEdK RSESRXRXYWZ RhSk[ RiSf[ R]C[I R]IZP RfKiX RTRS[ R`Q^Z RhDfM RgIkIiW',
	    'PoVGZG RdGhG RVI[I RdIhI RWQgQ RWTgT RWVgV Rb[l[ RSLSO RVEVL RWNWV RWNgNgV RdDiDiL RSLkLkO R]DaF RlXk[ R`C]F RaH^IaJ RZDUE RbWb[ R\\VYYS[',
	    'PoYE_E RbIjI RWK\\K RbMjM RXN^N RUP_P RWV^V Rh[l[ RWRWV RYIYM RYCYH RWR^R^V RTH_H_K RbDbRiRhZ RbDjDjR RlXl[ RXWXY R\\W[Z RbZ`[ RdRcY RTHTTR[ R_YU[',
	    'PoRDaD RcIkI RcNkN Rh[l[ RTIT[ RYDY[ R[N^P RcDcRjRi[ RcDkDkR RlWl[ RUNXP R[T^W RUUXW R]F`J RVFRJ RWLUR RWSUY R]L[R R]S[Y RbZa[ RSJ_J_[\\[ ReRcY',
	    'PoSD^D RaFlF RcGjG RSJ]J RSM^M RaMlM RWQgQ RWTgT RWWgW Rb[l[ RSDSL RWOWW RXDXG RXJXM RSG]G]J RaJaM RdJdM RWOgOhW RhJhM RaJkJkM RlXl[ RbD_G RbWb[ R\\WYZR[',
	    'PobIjI RbNjN RTN`N RUW_W RUZ_Z Rh[l[ RTFTI RUSU[ RZIZQ RZSZZ RUS_S_[ RTF`F`I RbEbRiRi[ RbEjEkR RlXl[ RYCZE RZN^P R]J\\N RWORR RdRcY R]HVI',
	    'PoRE`E RSH_H RbIkI RZL]L RSN_N RbNkN RTR^R RTU^U RTW^W Rh[l[ RSJSN RTPTW RYCYH RZJZL RTP^P^W RSJ_J_N RbDbSiSh[ RbDjDjS RlWl[ R[Y^Z RUXS[ RWJVL RbZ`[ RdSbY',
	    'PoSE`E RbIkI RTMXM RZM_M RbNkN RTQ_Q RUS^S RUV^V RUY_Y Rh[l[ RUOU[ RVCVG RZOZY RZIZM R\\C\\G RZI_I_M RbDbRjRh[ RbDkDkR RlWl[ RUNST RbZ_[ RTITM RTIXIXM RdRcY',
	    'Poa[k[ RVEVT RbRbZ RVEhEhT RlWk[ R]UZXWZR[ R_I_N]U',
	    'PoRF[F Re[k[ R_E_R RfRfZ R_EiEjR RlVk[ RbU]Z RVURY RULZW RTKUN RZLUX R[GWT RdHdPcU',
	    'Po_MlM RbWfW R[[k[ RTDTS R[S[Z RTD]D]S RfJfW RWVR[ RaEkEfJ RkWkZ RXHXQWW',
	    'PoSH\\H RRN\\N Rf[k[ R_E_R ReHeSfZ R_EjEjR RWCWR\\W RcU^Z RlVl[ RWMVW RdMcU',
	    'Po_EeE RWE`E RSE]E Ra[k[ RULUV RaTaZ RULhLhV RUGVJ RiGeJ RlXk[ R^F_J R^T[XT[ R^O^V RiCaD',
	    'Poe[k[ RXNX[ R^E^R RdHeSeZ R^EjEjR RWN[R RVDXF RcU]Z RlVk[ RYKRR RSH[HXM RdMcU',
	    'PoVH]H RRX[X Rf[l[ RRORZ RVCVO RRO[O[X R_D_S RfQf[ R_DjDjS RlWl[ RcV`Y[[ RdHdOcV',
	    'PoaGlG Ra[k[ RTETK RWNWV RZCZL RaUaZ RWNhNhV RdHhL RkXk[ RbC_K R_Q^U\\XXYR[',
	    'PoaZlZ RRHRL RVLVU RaTaZ RVLgLgU RRHkHkL RUDXG R^D_G RkWlZ RiDdH R^T[XWYR[ R^O^V',
	    'PoRJ\\J RSS\\S RgZlZ RRERJ RSSSZ RWCWJ RRN\\N\\S R\\E\\J R`E`S RgQgZ R`EjEjS RlWkZ RcV`Z\\[ R\\XRY ReHeOcV',
	    'PoSE_E Rg[k[ RYEYX RaEaS RfIgSg[ RaEkEkS RdU_[ RULWR R\\L_R RlVk[ RUISU R]I[T RfMeV R`WRY',
	    'PoTF^F RRT_T Rg[l[ RYCYJ RbDbS RgRgZ RbDjDkS RlWl[ RUMXN RSPVQ RXVTZ RdV_[ RZW^X RRJ^J]N RYLYQXV RfHfNdV',
	    'PoTM_M RRS`S Rg[l[ RTGTS RXCXS R[C[S RTG_G_S RbEbR RfHgRhZ RbEjEjR RlWl[ R\\V_Y RWVSZ RdW`[ RfMeW',
	    'PoVJ]J RSQYQ RSUYU Rh[l[ R\\[_[ RSMS[ R\\N\\V R_M_[ RbDbS RfHgRh[ RbDjDjS RlXl[ RYE]F`I RdWa[ RZCSJ RSMYMY[V[ RfMeW',
	    'PoSF^F RSI^I RRL_L RTS^S RRV_V Rg[l[ RZ[^[ RTOT[ RVCVL RYLYV R[C[L RTO^O^[ RaDaS RhQh[ RaDkDkS RlXl[ RcW`[ RfHfNdW',
	    'PoRF_F RUI\\I RSP^P RSS^S RSV^V Rg[l[ RSLSP RUCUI R]C]I RSL^L^P RaDaS RfHgRh[ RaDjDjS RlXl[ RdW_[ RfLdW RYIZYSZ R]YVZ',
	    'PoYE_E RYN_N Rg[l[ RYQYY RYCYH RZIZN R\\Q\\Y RTH`H`K RbEbS RfIgShZ RbEjEjS RlWl[ R_R^V RdW`[ RVSWV ReRbY RTHTTRZ RfMeV R]KUL R`XUZ',
	    'PoWOlO RVUlU Re[l[ RaJaU RVJlJl[ RVJVVUYR[ R[EVJSK R[EfEaJ',
	    'PoTO`O RTT`T R][`[ RZJZZ RSK`J`[ RdFdU RlDl[ RVF]F[J RlScU RWCTJTTSZ',
	    'PoUO_O RTT_T Rf[j[ R[[_[ RZJZT RSK_J_[ RdSa[ RVF\\FYL RaJlJlUk[ RWCTJTTSZ ReCeOcW',
	    'PoSJ[J R^OlO R^UlU Rh[l[ RXCX[ RfJfT R]JlJl[ R`G\\K RaC_I R^I^U[[ RaFhFeK R\\PRR RTERN R^P]Y',
	    'PoTO^O R_PlP RTT^T RZ[][ RZIZT RSK^J^[ R`HjHjP RgWl[ RcW_[ ReCeQiY RdRaY RVF[FYL RWCTJTTSZ ReLcW',
	    'Po`NlN RTO\\O RTT\\T RY[\\[ RYJYT RSK\\J\\[ R`F`Z RlXj[k[ RVF[FXL RjD`F RgEgOhZ RWCTJTTS[ RgY^Z',
	    'PoTO]O RbSeS RTT]T RZ[][ RZIZT RSK]J][ ReCeR RiHlN RVF[FYL RiPgUdY_[ RaH_O RWCTKTTSZ',
	    'PobMkM RTO\\O RTU\\U RZ[\\[ RYJYT RSK\\J\\[ RfVl[ RhUa[ RdOhX RjNfW RVF[FXK RdMfV R_EhEgM RVCSKTUS[ RbEbN`X',
	    'PoTO\\O RTT\\T RY[\\[ RYJYT RSJ\\J\\[ RdGdY RVE[EXK RiDiLiOkZ RVCTJTTS[ RjD`E`S^[ RgThY`Y',
	    'PoaMjM RTO]O RTU]U RaUjU R`[l[ RYIYT RaEaU RaEjEjU RVE[EYL RVCSK]J][[[ RTITTS[',
	    'PoYF^F RWSiS RVXiX Rc[i[ RTETK RWOWW R`P`W RVPiPi[ RYMcM`Q RZKWPSQ RbCaKlJkK RTZS[ RjEaG RYCYKRK R[JTK R]JWK',
	    'Po`LlL RTO\\O RTT\\T R_ZgZ RX[\\[ RYJYT RSK\\J\\[ R`E`V RVF[FXK RlVkZ RjD`E ReU^V RgDgOhY RVCSJTTS[',
	    'PoaFlF RaJjJ RTO]O RTT]T Rf[i[ RYJYZ ReVa[ RhOdX RVF[FYL RbC`I RdOaU RVCSK]J][[[ RgL`OkPj[ RTJTTS[',
	    'Po`GkG R_MlM RTO\\O R_SlS RTT\\T R_ZlZ RXJXT ReCeM ReOeZ RVF[FXK RVCSK\\J\\[Z[ RTITUS[',
	    'PoUGjG RRKlK RXThT RWWhW Rb[h[ R`Q`Z RWQhQhZ RWDYF R[NcN`Q RfDdF RdKfMlP R_C\\IXMRO R\\LXPTQ RVZT[ RYPXSWY',
	    'PoTO\\O RTT\\T RaZjZ RXJXT RaRa[ RaRjRj[ RbGdLhPlQ RUF[FYJ RcC`J RbGiFgKbP^R RVCSK\\J\\[Z[ RTJTTSZ',
	    'PoRE^E RSI]I RRL^L RgMkM RWUjU RVXjX Rf[j[ R`R`[ RURjRj[ RdI`M RYPdPaR ReFcK RXDYNWQRS RUZR[ R`ElEkM RWQVY',
	    'PofLjL RTO]O RaQlQ R_UlU RYKYT RfMf[ RbM_S R_EkEjL RdEcI_L RUF[FYJ RSK]K][Z[ RSKSTSWR[ RST]T RUFSK',
	    'Po^KlK RTO[O RTT[T RfTjT Rc[l[ RX[[[ RYJYT RSJ[J[[ RcNc[ RcNjNjT RlXl[ RUFZFXL RaFhFfJ RVCSJTTS[ RbC`I^J_T^Z',
	    'Po_NlN RTO]O RTT]T Rg[l[ RZ[][ RYJYT RSK]J][ ReCeN RhNh[ RlWl[ R`EaK RkEhK RVF[FYL RWCTKTTSZ RcNaY',
	    'PoTO]O R`QkQ R`ZkY RYJYT R`I`Q ReCeY R`IkIkQ RiTl[ RUF[FYJ RSJ]J][Z[ RSJSTSXR[ RST]T RUFSK',
	    'Po_IlI R`OkO RTO]O RTT]T R_TlT RY[][ RYJYT RSK]J][ ReIe[ RaCbH RVF[FYK RjChH RVCTJTTS[',
	    'Po_EkE R^KlK RTO\\O RTT\\T Ra[e[ RYJYT RSK\\J\\[ ReEeK ReMe[ RgTlX RkOgR ReNiV RVF[FXL R_PcP`W RVCSKTTS[',
	    'Po^HlH RTO\\O RTT\\T Ra[d[ RY[\\[ RYJYT RSJ\\J\\[ ReCe[ RhTlX RhDjF RbS^X R_KaP RfOiW RjKhP RVF[FXK ReJgT RWCTJTTS[',
	    'Po_GlG R^NlN RTO\\O R_TlT RTU\\U RZ[\\[ RXJXZ RSJ\\J\\[ ReNe[ RdDfE RaHcM RiHgN RUF[FXK RVCSKTTS[',
	    'Po^GlG RTO\\O R_QjQ RTT\\T RY[\\[ RYJYT RSK\\J\\[ R_K_Q ReCe[ R_KjKjQ RgTlY RePgT RbU^Z RePbV RVF[FXK RVCSJTTS[',
	    'Po_KcK RfKkK RTO[O R_PkP RTT[T Rg[l[ RX[[[ RYJYT RSJ[J[[ R`E`P RgPgZ RfEkEkP RlXl[ RaW^[ RUFZFXL RdD_E RbQaY RVCSJTTS[',
	    'Po_FkF RTO]O RTT]T RaVfV Rf[j[ RYJYT RaQaX RaQgQgV R_NkNj[ RdFhIkL ReHaL RVF[FYL ReCdJ RWCSK]J][Z[ RTITTSZ',
	    'Po`IjI R^MlM RTO\\O RTT\\T Ra[e[ RYJYZ ReMe[ R`EjEjM R_PbS ReRkY RkPgT RUF[FXL ReU^X RVCSK]J\\[Z[ RTJTTS[',
	    'Po_MkM RTO]O R_RkR RTT]T R^VlV RZ[][ RXJXZ RSK]J][ R_H_R ReHe[ R_HkHkR RbDcF RVF[FXK RiCgH RVCSJTTS[',
	    'Po_GhG R^MlM RTO\\O RTT\\T RaUjU RaYjY RY[\\[ RYJYT RSJ\\J\\[ RbPb[ RdCdL R`QjQj[ RUF[FXL RkEiIbP^Q RVCSJTTS[',
	    'PoUFkF RWH`H RXM_M RWUiU RVXiX Re[i[ RXJWM RXJ_J_M R`Q`X RVRiRi[ RdKhO RVWS[ RYPaP_S ReDiD RZNWQSS RhHeK`M RbCdL RlMlOiO RUFTN RWQVX',
	    'Po^GlG R_JjJ R^NlN RTO[O R_RkR RTT[T R^VlV RX[[[ RYJYT RSJ[J[[ ReOe[ ReGeM RaDbG R`P^S RgDfG RUFZFXL RVCSJTTS[',
	    'PoRE_E RTH^H RiLlL RUL\\L RTT]T RTX]X R[[][ RTQTX RYCYH RZQZW RSR]R][ RRJRK_J^M RbDbK RbDiDiL RVORS RVO[OYS RbPdVhYl[ RlIlL R`PjPhTdY_[',
	    'PoaFlF RaKjK RaNjN RTO\\O R^QlQ RTT\\T RX[\\[ RYJYT RSJ\\J\\[ RaHaN RaHjHjN RdU^Z RfWc[ RcQ^V RhTeY RVFZFXL RaC^J Rj[g[ R`TkTj[ RVCTJTTS[',
	    'Po^FlF R^JlJ RTO\\O R_RkR RTT\\T R_VkV RXJXZ R_N_V RbCbJ ReJeV RhChJ R_NkNkV RgXkZ RUF[FYJ RbY][ RVCSK\\J\\[Y[ RTJTTSZ',
	    'Po_IdI RfIkI RTO\\O R_PkP R_SkS RTT\\T R^WlW RY[\\[ RYJYT RSK\\J\\[ R_E_I R_L_S R_EdEdI ReLe[ RfEfI R_LkLkS RfEkElI RVF[FXL RWCSJTTS[',
	    'PoUGZG ReGiG RUJ[J RdJiJ RWUjU RVXjX Rc[j[ RSLSO RVEVL RaQaW RdDjDjL RWRjRjZ RSLlLlO RVWR[ RYPdP_R RaI\\K R]IbJ R`E]EaF RYPSR RZDUE RWRVX',
	    'Po_IlI RUO\\O RTU\\U R_UhU R_YhY RX[\\[ RYJYT RTJ\\J\\[ R_D_I R_P_T RdNdX RgEhI R_PhPhT R_DlDlI RgVhY RcEbI`M^O RVF[FXL RcHaL Rk[h[ R`MlMlVk[ RWCTJUTS[',
	    'Po_GlG R`JkJ R`MkM RTO\\O R`PlP RTT\\T RcWhW Ri[l[ RY[\\[ RYJYT RSJ\\J\\[ R_S_[ R`E`P RfEfP RhSlSl[ RVF[FXL R_ScScW RhShW RVCTJTTS[',
	    'PoTE\\E RTG]G R_IlI RTJ]J ReLkL RRL^L RePlP RXQcQ RVViV RUYiY Re[i[ RTDTG R_S_X RUTiSi[ RVXS[ RbFfH RaJ_P R_DkDdG RXNRP RYPWRRT RTJRK RXN\\O RYJXN RWRUY RfIeOjP',
	    'PoTO[O RbPfP R]SkS RTT[T RSJYJXT RdQd[ RdCcFfFfO RgUkY RaU]Y RjEhK RkHhP RUFZFWL R`H]P R`J\\J RbKfK RkJgJ RbFaN`P\\O RkMkPgP R_D[J[[X[ RVCSJTTS[',
	    'PodIiJ R_NkN RTO[O R_QkQ R_SkS RTT[T R`VlV RX[[[ RYJYT RSJ\\J\\[ R_L_S R_LkLkS R_F^IlHlK RbC]H RUFZFXL RaEhEdJaJ RbU^W RaWeZl[ RiWeY^[ RVCSJTTS[',
	    'Po_GlG R_KkK R`MjM RTO\\O R`PjP R`SlS RTT\\T RcXgX RX[\\[ Ri[l[ RYJYT RSK\\J\\[ R_U_[ R_D_G RaIaS R_UcUcX ReCeG RfIfS RgUgX RkDkG RgUlUl[ R`J]M RUF[FXL RVCSJTTS[',
	    'Po^FlF RaIgI RTO[O RbSfS RTT[T RiTlT RXJXT RaCaI RbMbS RcWc[ RbMfMfS RfWg[ RhChI RiKiT RjWl[ RUFZFXL R_W^[ R`K`S]T R`M]M RiNlNhN RVCSK[J[[Y[ RTITUR[',
	    'Po_GlG R`LjL R`OjO RTO\\O R`QlQ RTT\\T RfUjU RbZhZ Ri[l[ RX[\\[ RYJYT RSJ\\J\\[ R_D_G R_S_[ RaIaQ RbWbZ ReCeG RfHfQ RfSfU RbWhWhZ RkDkG R_SlSl[ RcTaW RaI]L RVF[FXL RaG`M RVCSJTTS[',
	    'PoWEgE RRIlI RWLgL RWPgP RVZgZ RVTV[ RVTgTg[',
	    'PoUEaE RRIdI RTLbL RTPbP RTZbZ RTTT[ RTTbTb[',
	    'PoTD[D R^FlF RRH\\H RSK[K RSO[O RSY[Y Ra[f[ RSSS[ RSS[S[Y RfFf[',
	    'PoSD\\D RRH]H RSK\\K RSO\\O RSZ\\Z RSSS[ RSS\\S\\Y RcCc[ RcLlP',
	    'Po_DiD R]HlH R_KiK R_NiN R_WiW R[[k[ RRI[I[Z R_R_W R_RiRiW RkXlZ RVCVNTY',
	    'PoSDZD RRH[H RSKZK RSOZO RSZZZ Rh[l[ RSSS[ RSSZSZY R]JhJiZ RlVl[ RaCaN_W',
	    'PoRH^H RSL\\L RSO\\O RSZ\\Z RSSS[ RSS\\S\\Y RbFbU RlDl[ RVDXF RlSaU',
	    'PoYJdJ RVMhM RXOeO RXReR RXYeY Re[j[ RXTXZ RXTeTeY RXGRM RYCWI RWGlGlSj[',
	    'PoSD[D RRH\\H RSK[K R]MlM RSO[O RSZ[Z RSSS[ RSS[S[Y ReCe[',
	    'PoSDZD RRH\\H RSKZK RSOZO RSYZY Rh[l[ RSSS[ RSSZS[Y R_EhEiZ RlWlZ RaLfQ R_E_Q^X',
	    'PoSDZD RRH\\H RSK[K R]NfN RSO[O RSY[Y RSSS[ RSS[S[Y RaEaR]Z R]EiEiUiZ RlVk[ RaL`W',
	    'PoRH]H RSK\\K RSO\\O RSY\\Y Rd[j[ RSSS[ RSS\\S\\Y RVCXF RaNfS RcC_L RaIlIlSj[',
	    'PoTD[D R^FlF RRH\\H RSK[K RSO[O RSZ[Z R]ZlZ RSSS[ RSS[S[Z ReFeZ',
	    'PoRH\\H RSK[K RSO[O RSY[Y RSSS[ RSS[S[Y RdTk[ RVCWF RdIeM RfS_[ R`JeU RkIeU R_FbQ R^FkFhP',
	    'PoTD[D RRH\\H R^JlJ RSK[K RSO[O RSY[Y Rc[h[ RSSS[ RSS[S[Y RhCh[ RaNcT',
	    'PoTD[D R^ElE RRH\\H RSK[K R]NlN RSO[O RSZ[Z R`[e[ RSSS[ RSS[S[Y ReEe[',
	    'PoTD[D R^ElE RRH\\H RSK[K R]NlN RSO[O RSZ[Z RSSS[ RSS[S[Y ReEe[',
	    'PoSDZD RRH[H RSK[K RSO[O RSZ[Z R`[k[ RSSS[ RSS[S[Z R`F`Z ReCeU RlWk[ RkI\\M RkIkTgT',
	    'PoTD[D RRH]H RTK[K RTO[O RSY[Y Re[j[ RSSS[ RSS[S[Y RbV][ RaK^Q RdP`Y ReLbW R_ElElSj[ ReGcT',
	    'PoTD[D RRH\\H RSK[K RSO[O RSY[Y RSSS[ RSS[S[Y RfEfX RlDl[ R`D`R^Y',
	    'PoTHjH RWKgK RRNlN RWPgP RWSgS RVZhZ RVVV[ R_C_H RVVhVh[ RjDjH RTDUH',
	    'PoTD[D RRH\\H RSK[K RSO[O R_XlX RSZ[Z RSSS[ RSS[S[Z R_J_[ ReDeX RlJlZ',
	    'PoTD[D R`HlH RRH\\H RSK[K RSO[O RSZ[Z R_[k[ RSSS[ RSS[S[Z RaU_Z RlWk[ R`MiM_W RbC^L',
	    'PoTD[D RRH\\H RSK[K RSO[O RSZ[Z Rd[k[ RSSS[ RSS[S[Z RdEd[ RlWk[ RjD^F RkN_P',
	    'PoTD\\D RRH\\H RSK[K R`NkN RSO[O RSZ[Z R`[k[ RSSS[ RSS[S[Z R`N`[ R_EkEkP RlVk[',
	    'PoRH[H RSKZK RSOZO RSY[Y RSSS[ RSS[S[Y RVCWF R^OeS RlYl[ RaR^Z R^IhIiTiZ RcCbMaW',
	    'PoUJiJ RXMfM RXQfQ RXXfX Rf[l[ RRJR[ RXTXY RXTfTfY R[ElEl[ RTDWG R]G`I',
	    'PoSDZD RRHZH RSKZK RSOZO RSYZY Re[k[ RSSS[ RSSZSZY R`H`[ ReDe[ RkHcP RlWk[ R_I[M RaC]K',
	    'PoRH\\H RSK[K RSO[O RSY[Y Rc[h[ RSRS[ RSR[R[Y RVCWF R`X][ R`J\\N RfFjN RbE_M RbR^Z RbN`X R^NiNiThZ',
	    'PoSDZD R^ElE RRH\\H RSKZK R_OlO RSOZO RSY[Y Rd[h[ RSSS[ RSS[S[Y RhEh[ RaW]Y RhO_X R`H_P',
	    'PoSD[D RRH\\H RSK[K R]NlN RSO[O RSY[Y RSSS[ RSS[S[Y RhWl[ RbU\\[ ReQiY RdEdP`X RdLgW RdKcT RjD^F',
	    'PoTD[D RRH\\H RSK[K RSO[O RSY[Y R^ZkY RSSS[ RSS[S[Y RhSl[ RgDlN RaD^M RcM_Y',
	    'PoSDZD RRH[H R_KeK RSKZK RSOZO RSZZZ R_[d[ RSSS[ RSSZSZY R^DeDeK RlCl[ R_K^RfRd[',
	    'PoSDZD RRH\\H RSKZK R`LhL RSOZO RSYZY RSSS[ RSSZS[Y ReElL RcCbF\\L R]QjQe[',
	    'PoTD[D RRH\\H RSK[K R_MlM RSO[O RSZ[Z RSSS[ RSS[S[Y RgMg[ RjD_F_R^Y',
	    'PoSD[D RRH\\H RSK[K RSO[O R]PlP RSY[Y RSSS[ RSS[S[Y R^HjHjP RfVk[ RbU][ RdCdRhX RcMbV',
	    'PoTD[D RRH\\H RSK[K RSO[O RSZ[Z RSSS[ RSS[S[Y R`NcTgYl[ R^FkK RiCcI\\L RiNfTbX\\[',
	    'PoSD[D RRH\\H RSK[K RSO[O RSY[Y Rh[l[ RSSS[ RSS[S[Y R^J^[ R^JlJl[ R`DeDfNiT ReMbT ReHdQ',
	    'PoSDZD RRH[H RSKZK RSOZO RSYZY Rd[k[ RSSS[ RSSZSZY R]I]M RdCdNeZ R]IlIlM RaU[[ RlWk[ RcP_Y RcHbU',
	    'PoRH\\H R^JlJ RSL[L RSO[O RSZ[Z Re[l[ RSSS[ RSS[S[Z RfNf[ RVCWF RgEjH RbR^[ RlWl[ RcCcLaW',
	    'PoRH\\H RSL[L ReNkN RSO[O RSZ[Z R\\[l[ RSSS[ RSS[S[Z R`I`[ ReCe[ RVCWF',
	    'PoRH\\H RSL[L RSO[O R^YlY RSZ[Z RSSS[ RSS[S[Z R^H^Y RlHl[ RVCXF RdR`U RaFbHdL RfMiT RhGbT RiDeP',
	    'PoTD[D RRH\\H R]HmH RSK[K RSO[O RSY[Y RSSS[ RSS[S[Y ReDeH RcHbPaT^Z RbOkOkTi[e[',
	    'PoSDZD RRH[H RSK[K RSO[O RSZ[Z Rb[i[ RSSS[ RSS[S[Z RbJb[ RbDeEgH RiWi[ RiLlU R^M\\U',
	    'PoTD[D RRH\\H RSK[K RSO[O R`RdR RSY[Y RSSS[ RSS[S[Y RdCdR RiGlN R_G]N RjOhUdX^[',
	    'PoTD[D RRH\\H RSK[K RhLlL RSO[O RSY[Y RSSS[ RSS[S[Y R`DhDhL RlIlL R`PaTeXl[ R]OjOhTdX\\[ R`D_L',
	    'Po^ElE RRH[H RSKZK R]OlO RSOZO RSYZY RSSS[ RSSZSZY RhEh[ RVCWF RaEaQ`W',
	    'PoSDZD RRH[H RSKZK RSOZO RSYZY RSSS[ RSSZSZY RaMeUiYl[ R_MkMhTcY_[ R_E_N]X R]EhEgM',
	    'Po^GlG RRH[H RSL[L RSO[O R_SkS RSZ[Z Re[k[ RSSS[ RSS[S[Y R_K_S ReCe[ RkKkV RVCWF RlXk[',
	    'PoTD[D R`HlH RRH\\H RSK[K RSO[O R]PlP RSZ[Z RSSS[ RSS[S[Y ReHe[ RaC]L',
	    'PoRH\\H RSK[K RSO[O R_RlR RSY[Y RSSS[ RSS[S[Y R_I_T ReCe[ R_IlIlT RVCWF',
	    'PoSD[D RRH\\H RSK[K R_NjN RSO[O RSY[Y RSSS[ RSS[S[Y R_EjEjN R_E_R]Y ReNfShWl[',
	    'PoTD[D RRH\\H RSK[K R_LlL RSO[O RSZ[Z RSSS[ RSS[S[Y RgLg[ RbPlU RjD_F_R^Y',
	    'PoSDZD RRH[H RSKZK RSOZO RgQlQ R]YlY RSZZZ RSSS[ RSSZSZZ R]E][ RgEgQ R]ElEl[ RbEbLaQ',
	    'PoSD[D RRH\\H RSK[K RSO[O R^SeS RSY[Y Re[j[ RSSS[ RSS[S[Y R^K^U R^KeKeS R]EkEj[',
	    'PoTDZD RRH\\H RSLZL R^MlM RSOZO R^SlS RSY[Y RSSS[ RSS[S[Y R^H^T ReCe[ R^HlHlT',
	    'PoRH\\H RSK[K R]LkL RSO[O RSY[Y RSSS[ RSS[S[Y ReEe[ RVCWF RhSlW R`T]X RdJiU RdK_V RjD^F',
	    'PoRH[H R]JkJ RSKZK RSOZO RSYZY RSSS[ RSSZSZY RdCd[ RVCWF RfEiH ReJjU RdJ^V',
	    'PoSD[D RRH\\H RSK[K RSO[O RSY[Y RSSS[ RSS[S[Y RcCkK RcC\\K ReJbM^O RgNdR^T RjRfW][',
	    'PoTEZE RRH\\H R]IkI RSLZL RSOZO R^QkQ RSZZZ R\\[l[ RSSS[ RSSZSZZ RdCd[',
	    'PoTD[D R^ElE RRH\\H RSL[L ReOlO RSO[O RSZ[Z R\\ZlZ RSSS[ RSS[S[Z R_L_Z ReEeZ',
	    'PoSD[D RRH\\H RSK[K RSO[O R_UfU RSY[Y Rf[j[ RSSS[ RSS[S[Y R_M_V R_MfMfU RaC]L R_IlHlUk[',
	    'PoYF^F RWMgM RROlO RWRgR RWTgT RVZhZ RTETK RVWV[ RVWhWh[ RaCaJkIkK RjE`G RYCYJRK R]JUK',
	    'PoRHZH RSLZL RSOZO RSZZZ Rf[l[ RSSS[ RSSZSZZ R^J^Z RaDaY RgDg[ RVCWF RlJfO RlWl[ RaMdMaM RdX[[',
	    'PoTD[D RRH\\H RdIlI RSK[K RSO[O RSZ[Z R_ZkZ RSSS[ RSS[S[Z R_Q_[ RdCdQ R_QkQk[',
	    'PoTD[D RRH\\H R]JlJ RSK[K RSO[O RSZ[Z R_ZkZ RSSS[ RSS[S[Z R_Q_[ ReCeQ R_QkQk[',
	    'PoRH[H RaJlJ RSKZK RSOZO RSZZZ Re[i[ RSSS[ RSSZSZY R`G`[ RiCi[ RVDVG RcNeT RaC\\N',
	    'PoSDZD R]HlH RRH[H RSKZK RSOZO RSYZY R_YkY R]ZeZ RSSS[ RSSZSZY R^McS RjWk[ RgTjX RePdT RbU^Y RhLfQ RdFaN RdDdI',
	    'PoRH[H R_IkI RSK[K RSO[O R\\PlP RSY[Y RSSS[ RSS[S[Y RfVk[ RVCWF RbV][ RdCdQhY RdPaX R`D]M RdLcU',
	    'PoSDZD RRH[H RSKZK R_LhL RSOZO RfXjX RSZZZ RSSS[ RSSZSZY RbPb[ R]PjPjX RcClK RcD\\L',
	    'PoTDZD RRH\\H RSLZL R_MlM RSOZO RSZZZ R][g[ RSSS[ RSSZSZZ R_F_W RjD^E RlUkYiY RfEfMgW ReV\\W',
	    'PoSDZD RRH[H RSKZK RSOZO R[QlQ RSZZZ RSSS[ RSSZSZY R^H^P R^HjHjP ReUk[ RdDdRgX RdQaW^Z[[ RcMbV',
	    'PoTHjH RWKgK RRNlN RWPgP RWSgS RWZhZ RTDTH RWVW[ R[D[H RbDbH RWVhVh[ RTDjDjH',
	    'PoSDYD RRHZH RSKYK RSOYO RSYYY RSSS[ RSSYSYY R]RbW R^WZ[ ReVa[ RfQjZ RiNdX RaP]Y ReLhW RjIgU RdGgU R^C[S R[IbI_U RcEkEhR',
	    'PoRH[H RSK[K R]MkM RSO[O R]SlS RSY[Y RSSS[ RSS[S[Y RdCd[ RVCWF R^FaK RjEhK',
	    'PoSDZD RRH[H RSKZK R_NkN RSOZO RSZZZ Rg[l[ RSSS[ RSSZSZY R_E_N RgNg[ R_EkEkN RlWl[ R`W\\[ RbN`X',
	    'PoRH\\H R]IlI RSK[K RSO[O R`QiQ R`YiY RSY[Y RSSS[ RSS[S[Y R`C`[ RiCi[ RVDXF',
	    'PoSDZD RRH[H R\\KlK RSKZK RSOZO RcShS R^ZlZ RSZZZ RSSS[ RSSZSZZ R^D^[ RcCcU RiDiU',
	    'PoTD[D R]ElE RRH\\H RSK[K RSO[O R_SjS RSZ[Z R]ZlZ RSSS[ RSS[S[Z R_E_Z R_KjKjS',
	    'PoRHZH RSKZK RSOZO RSZZZ Rg[j[ RSSS[ RSSZSZY RUCVF R]S\\W R`E[N RbK^T RbRcY RbV[W RdIlIlTkZ RgCgMeX RaM[N',
	    'PoTD[D RRH\\H R`IlI RSK[K RdNkN RSO[O RdTlT RSY[Y RSSS[ RSS[S[Y RdHd[ RaC]M',
	    'PoSD[D RRH\\H RSK[K RSO[O RSZ[Z Ra[k[ RSSS[ RSS[S[Z R^G^K RaLa[ ReCeG R^GlGlK RlWk[ RjN`S',
	    'PoTD[D RRH\\H RSK[K R]MkM RSO[O RSZ[Z R_ZjZ RSSS[ RSS[S[Z R_Q_[ R_QjQj[ RiIkO RcC_M',
	    'PoSDZD RRH[H R^IkI RSKZK RSOZO R\\PlP RSZZZ RbZkZ R_ZhZ R\\ZeZ RSSS[ RSSZSZY RdCdP RhUk[ RbP_Z',
	    'PoTD[D R_DlD RRH\\H RSL[L RfNkN RSO[O RSZ[Z R`ZkZ RSSS[ RSS[S[Z R`Q`[ R`QkQl[ RlEkN RdEcJ_N',
	    'PoTD[D R^EkE RRH\\H RSK[K RSO[O R\\QlQ RSZ[Z RSSS[ RSS[S[Y RdEd[ R_H`N RjHhN',
	    'PoSDZD RRH[H RSKZK RSOZO RSYZY RSSS[ RSSZSZY ReCeN R`NcTgYl[ R^NjNgTcY^[ R^HkHjL R^H^R\\Z',
	    'PoSDZD RRH[H RSKZK RSOZO RbShS RSYZY Ri[l[ RSSS[ RSSZSZY R^E^[ RbKbU RbKhKhS R^ElEl[',
	    'PoTDZD RRH\\H RSK[K R_NlN RSO[O R^YlY RSZ[Z RSSS[ RSS[S[Y R^Q^[ R_F_N ReCeY RlFlN RlQl[',
	    'PoRH[H R]JlJ RSK[K RSO[O RSY[Y RSRS[ RSR[R[Y RVCWF RfDiG RaCaOdUhYl[ RaNiNfUbY^[ RaJ_W',
	    'PoRH\\H RSL[L R_MjM RSO[O R_UjU RSZ[Z R^[l[ RSSS[ RSS[S[Z R_E_U R_EjEjU RVCWF',
	    'PoSD[D RRH\\H RSK[K R`LiL RSO[O R`SiS RSZ[Z R\\[l[ RSSS[ RSS[S[Z R`E`Z R`EiEiZ',
	    'PoSDZD RRH\\H R^KkK RSKZK RSOZO RSZZZ RSSS[ RSSZSZY R^F^K RdCdP RkFkK R`SdXl[ R]PjPhTdX][',
	    'PoSDZD RRH\\H RSKZK R]NlN RSOZO RSY[Y R`[e[ RSSS[ RSS[S[Y R]G]K ReMe[ R]GlGlK ReCeG',
	    'PoSD[D RRH\\H R^IhI RSK[K RSO[O R_TfT RSY[Y Rf[l[ RSSS[ RSS[S[Y R_M_W R_MgMgT R^ElEl[',
	    'PoRGlG RaNkN RRPlP RWSgS RWUgU RV[h[ RVWV[ RaGaM RVWhWh[ R\\CZIWLSN RbDhE RiIaKZM',
	    'PoSDZD RRH[H RSKZK R\\NaN RSOZO RSYZY R`[d[ RSSS[ RSSZSZY R^IdId[ RkLgQ RaDgF RaP^W ReLhSlW',
	    'PoSDZD RRH[H RSKZK RSOZO RSZZZ R_[c[ Rh[l[ RSSS[ RSSZSZY R]EcEc[ RfElEl[ RbP\\V RkHfM RjOiReV RbH]M',
	    'PoSD[D RRH\\H RSK[K RSO[O R_QgQ R_UgU RSZ[Z Re[j[ RSSS[ RSS[S[Y R_M_W R_MgMgU RaC]M R_IlHlTk[',
	    'PoTD[D RRH\\H RSK[K R_MkM RSO[O R_UkU RSY[Y R_ZkZ RSSS[ RSS[S[Y R`C`M R_Q_[ R_QkQk[ RlIkM RjF_H',
	    'PoRH[H RSKZK R]NkN RSOZO RSYZY Ri[l[ RSSS[ RSSZSZY R]D]N RcPcZ RiPi[ RlXl[ RVCWF RdFiK ReDbJ R_P_T^Y',
	    'PoSDZD RRH\\H RSKZK R`MhM RSOZO RSYZY R^ZiZ RSSS[ RSSZSZY R^R^[ R^RjRj[ ReFlM RdCfG RbG\\M RcCaH',
	    'PoSDZD RRH[H R]IlI RSKZK R]NeN RSOZO RSYZY RSSS[ RSSZS[Y RaNaX RiDkF ReW\\X RgCgRi[k[lX',
	    'PocEjE RWF^F RVOhO RXQgQ RXTgT RWZgZ RWVW[ RWVgVg[ RTIXK RaFgM RjGeK RYCSI R`CbI RZLdLlN RVG\\HXMRO',
	    'Po^GkG RRH[H RSKZK RSOZO RSYZY RSSS[ RSSZSZY RhYl[ RcTjZ RVCWF RhJlN RdCdF RaK^N RgOdV RaOeU`Y][',
	    'PoSDZD R^GkG RRH[H RSL[L R\\LlL RSO[O R]QlQ RSZ[Z Rc[h[ RSSS[ RSS[S[Z RdCdL RhLh[ R_SbV',
	    'PoRH\\H R^JjJ RSL[L RSO[O R^OjO RSZ[Z RSSS[ RSS[S[Y R^E^[ R^EjEjO ReUjZ RVCWF RcOfW RkReU RdY\\Z',
	    'PoSDZD RRH[H RSK[K RSO[O RSZ[Z Rc[k[ RSSS[ RSS[S[Z R]G]J RdLd[ RdCdG R]GkGkJ RlXk[ RiK^M RhR]S RkQ_S',
	    'PoSD[D RRH\\H R_KlK RSK[K RSO[O RSZ[Z RaZkZ RSSS[ RSS[S[Y RaQa[ RaQkQk[ RkD^F_R]Z',
	    'PoSDZD RRH[H R]KlK RSKZK RSOZO RfUjU RSZZZ Rb[k[ RSSS[ RSSZSZY RcNc[ RbNjNjU RlWl[ R_H[L R`FgFeJ RaC^J^S]Z',
	    'PoTEZE RRH\\H RSLZL R^MiM RSOZO R^TjT RSZZZ R\\[k[ RSSS[ RSSZSZZ RdMd[ RdEkL RdEaI\\M',
	    'PoRH]H RSL[L R_MlM RSO[O R_SlS R_YlY RSZ\\Z RSSS[ RSS\\S\\Z R_G_[ R_GlGl[ RVDXF ReCdG',
	    'PoTD[D R^GlG RRH\\H RSL[L R^NlN RSO[O RSZ[Z R_ZkZ RSSS[ RSS[S[Z R_R_[ ReCeN R_RkRk[',
	    'PoTD[D RRH\\H R]KlK RSL[L RSO[O RSZ[Z R_ZkZ RSSS[ RSS[S[Z R_R_[ ReEeR R_RkRk[ RjD`E',
	    'PoSDZD R]GlG RRH[H RSK[K RSO[O RSY[Y RSSS[ RSS[S[Y RfVl[ R_McP ReG_M RiKaR]T RkPdW\\[ ReDeG',
	    'PoTD[D RRH\\H R^IlI RSK[K R^NkN RSO[O R]TlT RSZ[Z RSSS[ RSS[S[Y ReIe[ R`CbH RiChH',
	    'Po^GlG RRH[H RSKZK RSOZO R`PjP R`UjU RSY[Y Rg[j[ RSSS[ RSS[S[Y RaJa[ R`MjLj[ RVCWF RbF^P RbC`M',
	    'PoSDZD RRH[H R^IjI RSKZK RSOZO R\\OlO RSZZZ Rf[l[ RSSS[ RSSZSZY RdCdO RgOg[ RlXl[ R_W[[ R`E\\L RaO_X',
	    'PoSDYD RRHZH RSKZK RSOZO RSYZY RSSS[ RSSZSZY RfDf[ RlDl[ RhKiQ RbKcQ R]K[Q R_D_R^Y',
	    'PoSDZD RRH[H RaIiI RSKZK RSOZO RbUhU RSZZZ Rh[l[ RSSS[ RSSZSZY R^E^[ RbMbV RbMhMhU R^ElEl[',
	    'PoRH[H RSKZK R]NkN RSOZO RSY[Y RSSS[ RSS[S[Y RdDd[ RVCWF RgTlX R`T\\X R^F`K RdLhT RjFhK RdL`T',
	    'PoVIlI RcLkL R[NhN RXPlP RZRiR RZUiU RZZiZ RZWZ[ RcHcL RZWiWi[ RZDSK RYFcF`H R_H\\KXM RVIVRUX',
	    'PoTD[D RRH\\H RSK[K RSO[O RSZ[Z RaZlZ RSSS[ RSS[S[Y RbRb[ RaRlRl[ R_JcLeN RbF]L RcCaI RbGjGgLbR]T',
	    'PoSDZD RRH\\H RSKZK RSOZO RSYZY R_ZjZ RSSS[ RSSZSZY R_Q_[ R^RkRj[ RaF\\K R`GbKfOlR RbC_I R`GiGgJbP\\R',
	    'PoSDZD R]GlG RRH[H RSK[K RSO[O RSY[Y RSSS[ RSS[S[Y RhWl[ RdV`[ RgIfSiY RfQcY RkMjR RbMaS RfNgV R_D_N]X',
	    'Po]EdE RfElE RRH[H RSKZK R]NdN RfNlN RSOZO RSYZY RSSS[ RSSZSZY RiEi[ RVCWF RaEaT][ RaO_X',
	    'PoSDZD RRH[H RSKZK RSOZO R]VhV RSYZY Re[j[ RSSS[ RSSZSZY R]L]X RhLhX R`MfS R`C\\L ReJaS R_HlGlTk[',
	    'PoTD[D R_HkH RRH\\H RSK[K R]NlN RSO[O R^SkS RSZ[Z R][l[ RSSS[ RSS[S[Z RdCdN RdOd[',
	    'PoTDZD R\\ElE RRH[H R_JiJ RSKZK RSOZO R_PiP RSYZY RSSS[ RSSZSZY R_E_W RiEi[ ReV]W RgV_W RjVcV',
	    'PoRH[H R^IlI RSKZK RSOZO R]QlQ RSZZZ RSSS[ RSSZS[Y RgIg[ RVCWF R`DaH RhCfI RaIaQ`W',
	    'PoSDYD RRHZH RSKZK RSOZO RSYZY Rf[l[ RSSS[ RSSZSZY RgCg[ RlWl[ RhPlT RaQ]R R`W\\[ R]G_L RkGhL RaCaR`W',
	    'PoSDZD RRH[H RSKZK RSOZO RcQlQ RSZZZ RSSS[ RSSZSZY R_HdJ RaGjGeM`SeU RdC`H\\I RfK\\P RbR]T RkRgWbZ\\[',
	    'PoSDZD R]GdG RRH[H R^KkK RSK[K RSO[O R\\PlP RSZ[Z RSSS[ RSS[S[Y RdCd[ RgTlY RaT\\Y RePgU RcP`V RdObT RjFaG',
	    'PoSDZD RRH[H R_IkI RSKZK R\\OlO RSOZO RSZZZ RSSS[ RSSZS[Y RdCd[ RgTlY RaT\\Y RdMhU RdMaU R`D]M',
	    'PoSDZD R]ElE RRH[H R`IjI RSKZK RaOjO RSOZO R`UkU RSZZZ R]ZlZ RSSS[ RSSZSZY R]E][ ReIeU',
	    'PoSDZD R]GlG RRH[H RSKZK R`LhL RSOZO R]PkP Rc[g[ RaP`UiTg[ RcC`I\\M RgGhJlN RSZSS[S[YSY',
	    'Po]ElE RRH[H RSLZL RSOZO R^SkS RSZZZ R\\[l[ RSSS[ RSSZS[Z RdOd[ RVCWF R^MjLkN RgHjK RcEaI]M',
	    'PoRHlH RZKdK RXQfQ RXTfT RWZgZ RWVW[ RWVgVg[ RVDXF R[HTO RgDcH R]C_G RWNgN RcHiNlO',
	    'PoUGiG RSKlK RTOjO RWRgR RWTgT RVZhZ RVWV[ RVWhWh[ R^M`O RXDYF RfDcG RdLlO R_C]HXMRO',
	    'PoSDZD RRH[H R^JiJ RSKZK RSOZO R^PkP RSZZZ Ra[h[ RSSS[ RSSZSZZ R_J_O RbTb[ R^EiEiJ RbRfS RkLkP RiXh[ RiTlY R^T\\Z',
	    'PoSDZD R]HlH RRH[H RSKZK R^NkN RSOZO Rb[h[ RbSb[ RdCdN RiTlY RbPfR RhXh[ R^T]Z RS[SS[S[ZSZ',
	    'PoSDZD RRH[H RSKZK RSOZO Ra[h[ RbSb[ RhXh[ RbPeQ RiTlY R^HfK R^T]Z RcFaK^O R^EkEkOfO RSZSS[S[ZSZ',
	    'Po]GkG RRH\\H RSK[K RSO[O R^QjQ RSY[Y RSSS[ RSS[S[Y R^K^Q RdCd[ R^KjKkQ RVCWF RfSlY ReQgU RdOfT RbS\\Z RdObT',
	    'PoSD[D RRH\\H R_KjK RSK[K RSO[O R_PjP RSZ[Z RSSS[ RSS[S[Y R_G_Z RdCdF R_GjGjP ReUk[ RjTfU RcPgX RfY\\Z',
	    'PoSDZD RRHZH RSKZK R\\LlL RSOZO RSYZY R\\[`[ RSSS[ RSSZSZY R`E`[ RlWk[ RhEkH RgUb[ RjNfX RcD\\F RdR[S RfCfMhY',
	    'PoSDYD RbElE RRHZH RSKYK RbOlO RSOYO RSYYY RaZlZ RSSS[ RSSYSYY R`K`Z RgEgZ R`M^Q[T R`F[K R_[\\[ R`C_H`Q',
	    'PoSDZD RRH[H R\\JkJ RSKZK R^OjO RSOZO R\\TlT RSYZY RSSS[ RSSZSZY RgFlK RdReWhYl[ RbC_J R`K]R RcU_Y[[ RdNcV',
	    'PoRE^E R`GlG RRPlP RVRhR RVThT RVZhZ RVWV[ RXCXL RgGgL RVWhWh[ RjCbD R]HRI RaE`J]L RWMeM',
	    'PoRHYH RSKXK RcMkM RSOXO RbVlV RSZXZ Rc[l[ RSSS[ RSSXSXY RgEgV R^WZ[ RUDUG R_R\\Z R[E`E[M`M^W RkDbE R[Q^VaZi[',
	    'PoSDYD RRHZH RSKYK RgMlM RSOYO RaVlV RSYYY RSSS[ RSSYSYY RcJcV RhEhV RkDbF R[E`E\\NaN^W[[ R\\R^XcZlZ',
	    'PoSDZD R]FlF RRH[H RSKZK RSOZO R\\TlT RSYZY R`[d[ RSSS[ RSSZSZY R]K]N RdCdK RdRd[ R]KlKlN R_OiOeR',
	    'PoSDZD RRH[H RSKZK R\\LeL RSOZO RSYZY Ri[l[ RSSS[ RSSZSZY RaEa[ RgFgU RlDl[ R`MeR RaK\\U RdD\\E',
	    'PoSDZD RRH[H R\\IlI RSKZK RSOZO RSYZY Re[i[ RSSS[ RSSZSZY R]EdEdO RdHkO R`X\\[ RdH^O R]QfQfUkTj[ RaQ_X RiD`E',
	    'PoSDZD R]ElE RRH[H RSKZK RSOZO R]SkS RSZZZ R\\[l[ RSSS[ RSSZSZZ RdSd[ RcLeQ RgLjO R_G]K`Q RjGgL RdGbL',
	    'PoSDZD RRH[H RSK[K RSO[O R_PjP R_UjU RSZ[Z Rf[j[ RSSS[ RSS[S[Y R_L_[ RdCdK R_LjLj[ RhElJ R`G\\J',
	    'Po_HjH RRH\\H RSK[K R^LjL RSO[O RSY[Y RSSS[ RSS[S[Y R]O]R R^DjDjK R]OlOlR RVCWF RaTeXk[ R_RiSeX^[',
	    'PoRH[H R^IjI RSKZK RSOZO RSZ[Z RSSS[ RSS[S[Y RVCWF RhLkO RfEjI RaP\\U RcD^I RaK]O R`QbTeXl[ R`RiRdW\\[',
	    'PoRH[H RSKZK RSOZO RSYZY R^ZjZ RSSS[ RSSZSZY R^S^[ R^SjSj[ RVCWF RcIeK RdDhIlJ R^NiNeR RdC]K',
	    'PoTD[D R^DkD RRH\\H RSL[L R]OlO RSO[O R_ZjZ RSZ[Z RSSS[ RSS[S[Z R_S_[ R^IiIiO R_SjSj[ RcDaO',
	    'PoTDZD R^GgG RRH\\H R\\KlK RSLZL RSOZO R]UlU RSYZY R`[d[ RSSS[ RSSZSZY RcCcK RdSd[ RiH_PiPfR RjEhI R`Q\\S',
	    'PoSDYD RRHZH R]HkH RSKZK RSOZO RSYZY RSSS[ RSSZSZY RiDkF RjNd[ R]NcNcV_W R]H]S[[ RgCgPhZk[mW',
	    'PoSDYD RRHZH R\\IlI RSKZK RSOZO R[QdQ RSYZY RSSS[ RSSZSZY RbKbX RhDkF RgUb[ RjMeW RlWk[i[ R^K^S\\Y RfCfMhY',
	    'PoTD[D RRH\\H R_IjI RSK[K R_NjN RSO[O R_RjR RSY[Y Rg[l[ RSSS[ RSS[S[Y R_D_R RgRg[ R_DjDjR RlWl[ RaW^[ RbRaY',
	    'PoSDZD R\\ElE RRH[H RSKZK RSOZO RSZZZ R[[l[ RSSS[ RSSZSZZ RcEc[ R_MbT RhMlU R_I]T RhIfT',
	    'PoSDZD RRH[H RbKjK RSKZK RSOZO R\\TlT RSYZY RSSS[ RSSZSZY RbDbK R^F^OiOiT RbDjDjK RgVl[ RaV\\[',
	    'PoSD[D R_HkH RRH\\H RSK[K R]MlM RSO[O RSZ[Z R_ZkZ RSSS[ RSS[S[Y R_R_[ ReCeQ R_RkRk[ R`D]K',
	    'PoTD[D R_ElE RRH\\H RSK[K RSO[O R^PlP R^UlU RSZ[Z RSSS[ RSS[S[Y R^K^[ ReKe[ RaGhJ RjFeH R^KlKl[h[',
	    'PoTDZD R]HlH RRH[H RSLZL RSOZO R^PkP R^UkU RSYZY Rh[k[ RSSS[ RSSZSZY R^L^[ ReCe[ R^LkLk[ RgDkE',
	    'PoSDZD R_GlG RRH[H RSKZK RSOZO R\\QlQ R^XlX RSYZY Rc[h[ RSSS[ RSSZSZY RbSeU RbLeN R`C]J R_JjJjUh[ R_J^Q]X',
	    'PoTD[D RaDjD RRH]H R_HlH RTK\\K RaKjK RTO\\O RaOjO RaZjZ RTZ\\Z RTST[ RTS\\S\\Y RaSa[ RaSjSj[',
	    'PoSD[D RRH\\H RSK[K RSO[O R_QjQ RSZ[Z Rf[k[ RSSS[ RSS[S[Y R_K_Q RgQg[ R_KjKjQ RlXk[ RaEhDjJ RaW][ R`G\\K RbQ`Y',
	    'Po^EkE RRH[H R`IkI RSKZK R^NlN RSOZO RSYZY RSRS[ RSRZRZY RaNa[ RhVlZ RUCXF RjQgT RePiX RfYa[ R^E^S\\[',
	    'PoTD[D RRH[H RSK[K RSO[O R^PkP RSZ[Z Rg[k[ RSSS[ RSS[S[Y R^H^P RgPg[ R^HkHkP R_CaG RlWk[ RiChG RbP`W][',
	    'PoSDZD R\\FkF RRH[H R^JkJ RSKZK RSOZO RSZZZ Rg[k[ RSSS[ RSSZSZY R]N]R RdCdJ RgQg[ RlWk[ R]NkNlR RaQ`W][',
	    'PoSDZD R\\FlF RRHZH RSKZK RSOZO RSZZZ Rf[l[ RSSS[ RSSZSZY R`M`[ R`C`I RfKf[ RgCgI RlXl[ RfRcS RkMeR R`L[R',
	    'PoTD[D RRH\\H R`IkI RSK[K R`MkM RSO[O R^UlU RSY[Y R^ZlZ RSSS[ RSS[S[Y R^Q^[ R`D`M R`DkDkM R^QlQl[',
	    'PoSD[D RRH\\H R^IlI RSK[K R_NkN RSO[O R_TkT RSY[Y R_ZlZ RSSS[ RSS[S[Y R_H_[ RfGfY RaC]L RgDeI',
	    'Po\\FkF RRH[H RSLZL R]MlM RSOZO R]QiQ RcUjU RSZYZ RcZlZ RSSS[ RSSYSYZ RdCdZ R]JiJiQ RUCVF R^UbZ R^T[[',
	    'PoSDZD RRH[H R^IjI RSK[K R^NjN RSO[O R]RlR RSZ[Z RSSS[ RSS[S[Y R^D^N RdDd[ R^DjDkN ReSlY RcS\\Z',
	    'PoSDZD RRH[H R^IlI RSKZK RSOZO RaRkR RSZZZ R`ZlZ RSSS[ RSSZSZY R`T`[ RaLaQ RfKfY RkLkQ R^DlDlI RlTl[ R^D^R\\Z',
	    'PoRH[H RSLZL R_LiL RSOZO R\\QlQ RSZZZ R`[d[ RSSS[ RSSZSZY R]G]J RdQd[ R]GkGkJ RUDWF RhTkW R_V\\Y RcCeG',
	    'PoRH[H RSKZK R]LlL RSOZO R`SjS R`VjV RTYZY RTST[ RTSZSZY RaNa[ RVCWF RaN\\S R_DjI RhD^I RbI_QjPj[f[',
	    'PoSDYD R\\GlG RRH[H RSKYK RSOYO R[TlT RSYZY RSSS[ RSSZSZY RdCdG RdQd[ R_LbO RgLkP RhIeO R`I]O',
	    'PoSDZD RRH\\H RSK[K R`LjL RSO[O RaPjP RaWiW RSY[Y Rh[l[ RSSS[ RSS[S[Y R^E^[ RbPbW ReLeP R^ElEl[ RaGbJ RiGgJ',
	    'PobHlH RRHZH R[K`K RSLYL RSOYO RbQhQ R[Q`Q RSZYZ R[[_[ RSSS[ RSSYSYY R[K[Q R[E`E`K RUCVF RfCgG RjVl[ ReVcZ RjMeW RfIbQ R`R_[ RjYbZ',
	    'PoSDZD RRH[H R\\HaH RfHlH RSKZK R\\NaN RfNlN RSOZO RfTlT RSYZY RSSS[ RSSZSZY RfCf[ R`W\\[ RbCbT_Z RcS[T RaO`X',
	    'Po]GkG RRH[H RSKZK RSOZO RSYZY RSSS[ RSSZSZY RdCd[ RVCWF R_MbO ReRlY RhMlP RbS\\Y RdOfT RiJfP R`J]Q RdObT',
	    'PoTDZD RRH[H RSLZL RSOZO R^UjU RSYZY R^ZjZ RSSS[ RSSZSZY R^Q^[ R^QjQj[ RfIkM RjEgI RaJ]N RdDgK R]GbG_L RdCdOaO',
	    'PoSDZD RRH[H RSK[K RSO[O R`PiP R`UiU RSZ[Z R\\[l[ RSSS[ RSS[S[Z R]G]J R`K`Z RdCdF R`KiKiZ R]GlGlJ',
	    'PoRH\\H R_KcK RfKkK RSK[K RSO[O R_PkP RSY[Y Rf[l[ RSRS[ RSR[R[Y R_E_P RgPgZ ReEkEkP RlXl[ RVCWF RaW][ RcC^E RbQ`Y',
	    'PoRH\\H RbHlH RRK\\K RbKlK RYMeM RVOhO RYReR RYTeT RYYeY Rg[l[ RRER[ RYVYZ RRE\\E\\K RbEbK RYVeVeZ RbElEl[',
	    'PoSDZD RRH[H RaIiI RSKZK R`MjM RSOZO RaWiW RSYZY Rg[l[ RSSS[ RSSZSZY RaQaX ReFeM RaQiQiW R^ElEl[ R^E^R\\Z',
	    'PoRH[H R^KkK RSKZK RSOZO R^PkP R]UlU RSY[Y RSSS[ RSS[S[Y R^F^P RfQf[ R^FkFkP RVCWF RdO_T ReFeMbR RcCcF',
	    'PoRH[H RSKYK RSOZO RSYYY RTRT[ RSSYRZY RgVl[ R`UcX RUCWF RgIkM RjHdN RaV\\[ RaI]N ReQhY R]HcJ R]TaV ReEkEhK R]EcE_L R]QcQ_Y RdQkPiVfYb[',
	    'PoTD[D RRH\\H RSK[K RfLjL RSO[O R_TdT RgTlT RSZ[Z R_ZlZ RSSS[ RSS[S[Y R_P_[ RfOlOl[ RaH]L RcC_K RdN_P RbFlFkL',
	    'PoSDYD RRHYH R[JbJ RcJkJ RSKYK RSOYO RSZYZ RSSS[ RSSYSYY R^C^[ RgCg[ R^LbP ReNbV RgHjU R^I[U RgHcT',
	    'PoSDZD R]FlF RRH[H RSK[K R_MjM RSO[O R]UlU RSY[Y R`[d[ RSSS[ RSS[S[Y R_I_M RdCdF RdTd[ R_IjIjM R^QjQdS',
	    'PoSFjF RXHeH RXKeK RRMlM RWSfS RWUfU RWZgZ RWWW[ RWWgWg[ ReCeMhQ R_N_P RYCYMUQgPlR RWPRR',
	    'PoTDZD R]HlH RRH[H RSKZK R`LiL RSOZO R`PiP R\\UlU RSZZZ RSSS[ RSSZSZY R`C`U RiCiU RgVl[ RaW][',
	    'PoSD[D RRH\\H RSK[K RSO[O RSZ[Z RSSS[ RSS[S[Y RjEiI RjQiU R`E^I R_Q^U RdDdHaL^N RdHfLjM RdPdTcWaY][ RdTeWgYj[',
	    'PoSDZD RRH[H R\\JlJ RSKZK R\\OlO RSOZO R\\TlT RSZZZ R\\[l[ RSSS[ RSSZSZZ R_J_T R]EdEd[ RiJiT RjDbE',
	    'PoSDYD RRH[H R\\IlI RSKYK RSOYO R\\RlR RSYYY RSSS[ RSSYSZY R]EdEeO RdHhLlN RdH`M\\N RbO_WjZ RhReX\\[ RiD`E',
	    'PoSDZD RRH[H RSKZK RSOZO R\\QiQ RSYZY Rf[j[ RSSS[ RSSZSZY RcJcZ RbQhV RaS\\X R^K_O RgKeO R`C\\L RcO`T R^HlHlTk[',
	    'PoSD[D R]FkF RRH\\H R^IkI RSK[K R]LlL RSO[O R_SjS R_VjV RSZ[Z Rg[j[ RSSS[ RSS[S[Y R_O_[ RdCdL R_OjOj[',
	    'PoSDZD R\\FlF RRH[H RSK[K R^NjN RSO[O R^RjR RSZ[Z RSSS[ RSS[S[Y R^J^R RdCd[ R^JjJjR ReSlZ RcS\\Z',
	    'PoSDZD RRH[H RSKZK RSOZO R\\PlP R]UjU RSZZZ R_[c[ RSSS[ RSSZSZY RdLd[ RcFdJ R^LjLjV RhFkI R^E\\K RiD_E',
	    'Po^GkG RRH[H RSKZK R]MlM RSOZO R_UjU RSY[Y R_ZjZ RSSS[ RSS[S[Y R_P_[ RaCaM RhChM R_PjPj[ RVCWF',
	    'PoSDYD R[ElE RRHZH R]KcK RSKYK RSOYO R]QcQ RSYYY RSSS[ RSSYSYY R]E]W RcEc[ RfRjZ RjQfZ ReKhW RdJkJiW RbV[W',
	    'Po[GiG RWNgN RRQlQ RWSgS RWUgU RWZgZ RWFWM RWWW[ R[D[G RbIbL RWWhWh[ R[DiDiG RhIhMkLkM RYDRJ R\\I[LYM',
	    'PoSDYD R\\FkF RRH[H RSKZK RSOZO RSYZY RSSS[ RSSZSZY ReQeY RhTlW R`V[X RdQ^W RbM^O`R RkKfNjV RdF`J\\K R`P[Q ReIhT Rd[`[ RbKdPeW ReFgR',
	    'PoSD[D R]GlG RRH\\H RSK[K RSO[O R_QkQ RSZ[Z Ra[d[ RSSS[ RSS[S[Y R_K_Q ReQe[ R_KkKkQ RjTlY R_T]Y ReCeG',
	    'PoTDYD RbGlG RRH[H RSLZL RSOZO RaSjS R^SgS R\\SdS RSYZY RSSS[ RSSZSZY RcIfMiN RbVeZi[ RkLlN R`ObW RaCcJ ReOhP RfDjE R]HcHiHdL\\N RhVbY[[ RlYl[i[',
	    'PoSDYD R_HcH RRHZH RSKYK R[MdM RSOYO RSYYY R\\[_[ RSSS[ RSSYSYY R_C_[ RfVb[ RbQdV RgQjZ RiOeY ReLiX R[Q[W ReHhV RdFkFhU',
	    'PoTDYD RdFlF RRH[H RSKZK RSOZO RdQkQ RSYZY Rg[l[ RSSS[ RSSZSZY RdKdP RdCdI RgSg[ R]IlIkL RlXl[ R`Z][ RbT`Y ReM_N R]I]S\\Z RiMcM',
	    'PoSDZD RRH[H R_KhK RSKZK RSOZO R]UjU RSYZY RSSS[ RSSZSZY R]O][ RbOb[ RfOf[ RdCkK RdC\\K R]OjOj[h[',
	    'PoSDYD RRH[H R`IhI RSKYK RSOYO RSZYZ Ra[g[ RSSS[ RSSZSZZ RaTa[ RgYg[ RaReU RiUkZ RdCjJ RcC\\J R]MiMfR R]U[Z',
	    'Po]GkG RRH[H R^KjK RSLZL R]OlO RSOZO RTZZZ RTST[ RTSZSZY RaRaZ RgUlZ RVCWF ReCdO]U RjQgU RdNhW ReX_Z RdKeR',
	    'PoRH[H R]IlI RSKZK RSOZO R^ViV RSYZY R^ZiZ RSSS[ RSSZSZY R]E]O R^Q^[ R^QiQi[ RVCWF RgNkP RlMlP R]EdDgN RcN\\N RiCaD',
	    'PoSDZD RRH[H RSK[K R^LkL RSO[O RdRkR RSZ[Z Rd[l[ RSSS[ RSS[S[Y R]G]J ReLeZ R]GkGkJ R`WdZ R_TaY RdCdG R_Q]Z',
	    'PoSDZD RRH[H RSK[K R]LaL RfLkL RSO[O R]QkQ RSY[Y RSSS[ RSS[S[Y R]G]S RfFkFkR RfVj[ RdCdRgX RdQbW_Y\\[ RbF]F RdNcV',
	    'PoSDZD R\\GlG RRH[H RSKZK RdKiK R_OkO RSOZO R\\SlS RSZZZ RSSS[ RSSZSZY R_D_O RdCdK RdOd[ RiDiK ReTkZ RdR]Y',
	    'PoTDZD RRH\\H RdHjH RSLZL RcMlM RSOZO R_SjS R_WjW RSYZY Rf[j[ RSSS[ RSSZSZY R_O_[ ReDeL R_OjOj[ R]ElDjG R`G]M',
	    'PoSDZD R]DlD RRH[H RSKZK R^MkM RSOZO R_UlU RSYZY Ri[l[ RSSS[ RSSZSZY R^H^S_[ RcQc[ RhQh[ R^HkHkM R_QlQl[ R^O\\Y',
	    'PoTDZD RRH\\H R_HjH RSKZK R_LjL RSOZO R\\PlP RdTkT RSYZY Rc[l[ RSSS[ RSSZSZY R_D_L RePeZ R_DjDjL R_VcZ R_T\\Z',
	    'PoSDZD RRH[H R`JiJ RSK[K RSO[O R_RjR R_WjW RSZ[Z R\\[l[ RSSS[ RSS[S[Z R]F]J R_N_W RdCdG R_NjNjW R]FlFlJ',
	    'PoSDZD RRHZH R_HhH RSKZK RSOZO RcQgQ RSZZZ R[ZlZ RSSS[ RSSZSZZ R^S^Z RbSbZ RfSfZ R`LhLgQ R^SjSjZ RbEhDlI R`E\\I RaH`M',
	    'PoSDZD RRH[H R^IkI RSKZK RSOZO R^PkP R^TkT R\\WlW RSYZY RSSS[ RSSZSZY R]D]G R^L^T RdGd[ R^LkLkT R]DlDlG',
	    'PoRH[H RSK[K RSO[O RbWhW RSY[Y Rh[l[ RSSS[ RSS[S[Y R]N][ RbRbW R`IeIeN RbRhRhW R`DjDjN R`D_NlNl[ RVCWF',
	    'PoSDZD RRH[H R]JcJ RfJkJ RSK[K R^MjM RSO[O R\\QlQ RSY[Y Rc[h[ RSSS[ RSS[S[Y R]D]J R]DcDcJ RfDfJ RfDkDkJ RaQ_ViUh[',
	    'PoRH[H R]HlH RSLZL RSOZO R^OjO R^SjS R^WkW RSZZZ R\\[l[ RSSS[ RSSZSZZ R^K^S R]EeEd[ R^KjKkS RVCWF RjDbE',
	    'PoSDZD R]FlF RRH[H RSKZK RSOZO RgXkX RSYZY RSSS[ RSSZSZY R]K]O R^P^X RdCdF RdMd[ R^PkPkX R]KlKlO R`GaK RhGgJ',
	    'PoSDZD R^GcG RRH[H RSKZK RfNlN RSOZO R^VjV RSZZZ R^ZjZ RSSS[ RSSZSZY R^C^N R^Q^[ RfCfN R^QjQj[ RlKlN RcObQ RkFeI RcM\\N',
	    'Po]ElE RRH\\H RSK[K R_MjM RSO[O R^UkU RSY[Y R^ZkZ RSSS[ RSS[S[Y R^P^[ R_H_M RdPdZ R_HjHjM R^PkPl[ RVCWF',
	    'PoRH[H R]IlI RSLZL RSOZO RSZZZ R\\[l[ RSSS[ RSSZSZZ RbIb[ RfHf[ RVCWF R`CaG RhCeI R^M_U RjMhU',
	    'PoSDZD R]FlF RRH[H RSKZK R_MjM RSOZO R^SjS RSZZZ R`[d[ RSSS[ RSSZS[Y R\\P\\S RdCdF RdSd[ R_IjIjM R\\PlPlS R_I_M',
	    'PoSDZD R\\FkF RRH[H RSKZK RSOZO R]RkR RSYZY RSSS[ RSSZSZY R]J]R RdCd[ R]JkJkR RdRlY RbS\\Y R`LaP RgLgP',
	    'Po_FlF RRG^G RRQlQ RWSgS RWUgU RWZgZ RWWW[ RSEYDYM RWWhWh[ RhCjE ReH`L RYGTL RXH^K RWNfNkKkM ReFeM ReCdJ R]DUD',
	    'PoSDYD RRHZH R_JhJ RSKYK RSOYO R]RbR R]VbV RSYYY R`[b[ RSSS[ RSSYSZY R]N][ R]NbNb[ RfNfV RdCiIlJ RcC\\J RjMj[g[',
	    'PoSDZD RRH[H RSKZK RSOZO RSYZY R_ZjZ RSSS[ RSSZSZY R_R_[ R_RjRj[ R]D`G RgLkO RcGkGkJ RdCaJ RaK\\M ReKcN`P RfFgM',
	    'PoRH[H R^JkJ RSKZK R\\MlM RSOZO RSY[Y Ri[k[ RSSS[ RSS[S[Y R]Q][ R^E^J RbQb[ RdMcQ RdCdJ RgQg[ RkEkJ R]QkQk[ RVCWF',
	    'PoTEZE RRH\\H R^JjJ RSLZL R^OjO RSOZO RSZZZ Ra[h[ RSSS[ RSSZSZZ R^D^O RbTb[ RdDdO RhXg[ R^DjDjO RiTlY RbQfS R^T\\Z',
	    'PoSDZD RRH[H R]JlJ RSK[K RSO[O R_QjQ R_TkT R\\XlX RSY[Y RSSS[ RSS[S[Y R_T_X R_L_Q RfQf[ R_LjLjQ R^FiFiJ RbCbJ',
	    'Po\\ElE RRH[H RSLZL RSOZO R]OkO R]UkU RSZZZ R\\[l[ RSSS[ RSSZSZZ R]I]O RbEbO RdQd[ RfEfO R]IkIkO RVCWF',
	    'PoSD[D R]FlF RRH\\H RSK[K R\\MlM RSO[O R_UjU RSZ[Z R_ZjZ RSSS[ RSS[S[Z R_Q_[ RdCdF R_QjQj[ R`HaL RhHgL',
	    'PoSDYD R\\HlH RRHZH R_KdK RSKYK RSOYO R_UdU RSYYY RSSS[ RSSYSYY R_O_W R_OdOdU RhDkE RgUb[ RiZk[ RkMfW RlWl[ RgCgPhZ R\\H\\T[[',
	    'PoSDZD R]GlG RRH[H RSKZK RSOZO R`QiQ R_UjU RSYZY Rh[l[ RSSS[ RSSZSZY R]K][ RdCdK RdQdZ R]KlKl[ RaMbP RgMfP',
	    'PoTDYD R\\FlF RRH[H R_JiJ RSLZL R_MiM RSOZO R[QlQ RfVkV RSYZY R]ZkZ RSSS[ RSSZSZY R]Q][ R_C_Q RfQfV RiCiQ RbR`W',
	    'PoSDYD RRHZH RSKYK RSOYO R_SgS R^YgY RSYYY RSSS[ RSSYSYY R_L_S RcHcX R_LgLgS RfUhZ R\\DjDjWkYl[ R\\D\\S[Z RfG^H',
	    'PoSDZD R^GhG RRH[H RSK[K R\\LlL RSO[O R`UjU RSY[Y R`ZjZ RSSS[ RSS[S[Y RaPa[ RcCcL R_QjQj[ RjGbO\\RXS',
	    'PoRH[H R_HjH RSKZK R_LjL RSOZO R]OlO RSYZY RSRS[ RSRZRZY R_D_L R_DjDjL RVCWF R`R\\V RbV]Z RgRc[ RcR`X Rj[g[ RaO_TkSj[',
	    'PoTDYD R]FlF RRH[H RSLZL R]MlM RSOZO RSYZY RSSS[ RSSZSZY RdCdF R`HiJ RiRaV RgI_K RhO`Q RkWfY_[ R]M]T\\Z',
	    'PoRH[H R^JjJ RSL[L R^NjN RSO[O R]RkR R^VjV RSZ[Z R\\[l[ RSSS[ RSS[S[Z R^F^N RdRd[ R^FjFjN RVCWF RcCcG',
	    'PoSDZD RRH[H R^KjK RSKZK R\\NlN RSOZO R`RiR RSZZZ RSSS[ RSSZSZY RhEkH RcFcI RaSeXl[ R^E\\J R`R\\[ RiTeX^[ RaK^W RiC`E',
	    'Po]GlG RRH[H RaJhJ RSK[K RSO[O R^SjS R]VlV RSY[Y RSSS[ RSS[S[Y R^N^S RaDaJ RdJd[ RhDhJ R^NjNjS RVCWF',
	    'PoSDZD R\\FlF RRH[H RSK[K R\\MlM RSO[O RSZ[Z RaZkZ RSSS[ RSS[S[Y RaCaJ RaSa[ RhChJ RaSkSk[ RaQ\\U RcJ_S',
	    'PoTDZD RRH[H R_IjI RSKZK R\\LlL RSOZO R_PiP R_TiT ReWlW RaXiX R]XdX RSYZY RSSS[ RSSZSZY R_D_I R_L_X RiLi[ R_DjDjI',
	    'PoSDZD R]FlF RRH[H R_JiJ RSK[K R_OiO RSO[O R\\RlR RSZ[Z RSSS[ RSS[S[Y R_C_N RdOd[ RiCiN RfTlZ RbT\\Z',
	    'PoSDZD RRH[H R_HkH RSKZK R_MkM RSOZO RSYZY Rf[k[ RSSS[ RSSZSZY R_D_M R_Q_X R_YgXgY R_DkDkM RaN\\S RfS_U R_QlPlUk[',
	    'PoSD[D RRH\\H R^HlH RSK[K R^LlL RSO[O R_SkS R_WkW RSY[Y Rg[k[ RSSS[ RSS[S[Y R^D^L R_O_[ ReDeL R_OkOk[ R^DlDlL',
	    'PoRH[H R^IjI RSL[L R^MjM RSO[O R]RkR R^VkV RSZ[Z R\\[l[ RSSS[ RSS[S[Z R^D^M RdOd[ R^DjDjM RVCWF R_O\\T',
	    'Po\\GjG R[KlK RTKYK R`NgN R]PjP RTRYR R^RhR R^UhU R^ZiZ RTDYDY[ R^W^[ R^WiWi[ RfJkO R`J[O R]D^F ReGgL RbD_L RhCgF RV[Y[V[ RTDTSR[',
	    'PoSDZD RRH[H RSKZK R_KhK RSOZO R_WhW RSYZY Rg[j[ RSSS[ RSSZSZY R_T_W R_G_K RhHhK RhThW RaM`Q\\T R`E\\I RcM^O RbY^[ RkNgN RcGcM R_FlFkN R_RlRk[ RcScY',
	    'PoSDYD RRHZH RSKYK R]MkM RSOYO RSZYZ Ra[d[ Ri[l[ RSSS[ RSSYSYY R]PdPd[ R]HkHkL RfPlPl[ RbP_T RdT_X RkPgT RlSgX R]E]R\\Z RcE]E RjC`E',
	    'PoVHiH RVJhJ RRMlM RZOdO RVQhQ RWSgS RWUgU RW[g[ RSESH RWXW[ RWXgXg[ RSEkEkH ReGdMfOlQ RZGZMWPRR',
	    'PoVGhG RVJhJ RTLjL RROkO RWTgT RV[g[ RVDVJ RVXV[ RZJZO RdJdO RVXgXg[ RVDhDhJ RURgQlS RYPRS RdVgV RYVVV RbVdV R^VaV RXV[V^V R[VYV ReVcV`V]V',
	    'PoRH[H R]KjK RSKZK RSOZO R]PjP RSYZY RSSS[ RSSZSZY R]F]P R]FjFjP RdPd[lZl[ RVCWF R`V[[ RhRfW RbP_Y RjTkWeW RcCcFcKbT',
	    'PoSDZD R\\FlF RRH[H R]KlK RSKZK RSOZO R^PkP RSYZY Ri[l[ RSSS[ RSSZSZY R^K^P RaCaI RcRcZ RdHdK RhChI RiRi[ RlXl[ R^R]Y',
	    'PoSDYD ReGkG RRHZH RSKYK RcMlM RSOYO R[SkS RSYYY RSSS[ RSSYSYY RcPc[ ReGeL RiDiP RhXlY RcRjY RcR\\Y R\\I`K\\P R^D\\J R^GbG_M',
	    'PoSDZD RRH[H RSKZK R_LeL RSOZO R\\PlP R^XeX RSYZY Re[i[ RSSS[ RSSZSZY R^S^Y R_H_M R_HeHeL R^SeSeX RiPi[ R]EkEiNfN',
	    'PoWNhN RSPlP RWRhR RWThT RWZhZ RWWW[ RWWhWi[ RSKSMlLlN RkEiG RUESG R^E\\G RbE`G RXG]J ReHkJ RfDdH`J RYCXHRJ',
	    'PoSDYD RRH[H RSLYL R`MlM RSOYO RSYYY RSSS[ RSSYSYY RfCfX R\\J^L R\\D^F R[Q_Q^X[[ RaEbJ RfMkU RjEiK RfMbT R^Xa[l[',
	    'PoSDYD RRH[H R]JaJ RgJkJ RSKYK R]NkN RSOYO RSYZY RSSS[ RSSZSZY R]F]N RdCdR RfEkEkN R_ScWj[ RaD]F R\\RjReWaY[[',
	    'PoSDZD RRH[H RSKZK RSOZO RaPhP RSZZZ R[[l[ RSSS[ RSSZSZZ R^S^[ RbSb[ RfSf[ R^SjSj[ RjIlM RiFaN RhMhP RaDfF R^I\\M RaGbO[P',
	    'PoSDZD RRH[H RSKZK RSOZO R\\RkR R\\UlU RSYZY RSSS[ RSSZSZY RhOlR RhFlI RgK`Q RcFdI RbJ_LcN RcUgYl[ R^E\\J RcSbW`Y[[ RiC^E',
	    'PoRH\\H RSKZK RSOZO R_OjO R_RjR RSY[Y R_ZjZ RSSS[ RSS[S[Y R_L_U_[ R_LjLjR R^UjUj[ RcIeJ RdDjJ RdD^J RWCWF R_O]Y',
	    'Po]FkF RRH[H RSKZK R_LiL RSOZO R_OiO R_SiS R\\VlV RSYZY RSSS[ RSSZSZY R_I_V R_IjIjV RhWk[ RVCWF RaW^Z RdCcI',
	    'PoSDZD RcFkF RRH[H RSKZK RSOZO RcQkQ RaSkS R_VlV RSYZY RaZkZ RSSS[ RSSZSZY RaSa[ RcJcP RcCcI R]IlIkL R]I]S\\Z RfM_M RhLbM',
	    'PofHlH RRHZH RSKYK RSOYO ReRlR RSYZY Rh[l[ RSSS[ RSSZSZY RfLfR RfFfK RdThTi[ R`KkKkN RlXl[ RUCVF RdTcZ R]F]S\\Z R`K`S_Z RdE]E RjD_E RhNbO',
	    'PoSDZD R^HjH RRH[H R^KjK RSKZK RSOZO RSYZY RSSS[ RSSZSZY R^D^K R^DjDjK RfPkNkP RaL]P RbP\\V R`SdXk[ RdDeKfP R`SiSeWaY\\[',
	    'PoSDZD R]FlF RRH[H RSKZK RSOZO R\\PlP RSYZY Rd[h[ RSSS[ RSSZSZY R]K]O RdCdF R]KlKlO RaW][ R`GaK RhGgK RdMcP`Y RbTiTi[',
	    'PoRH[H R]IlI RSKZK R_LkL RSOZO R_RlR R`VlV RSYZY RSRSZ RSRZRZY RfRf[ R]DlDlI RUDWF ReJfL RiOfR RbNbQ R]D]R\\[',
	    'PoSDZD R\\HlH RRHZH RSKZK RSOZO R[OlO R]SjS RSZZZ RSSS[ RSSZSZY RaHa[ ReHe[ R]KjKjS ReSlZ R_C`F R`U[Z RhCgF RaR_W',
	    'PoSDZD RRH[H R`IhI RSKZK RbMgM RSOZO RSZZZ R[[l[ RSSS[ RSSZSZZ R^T^[ RbTb[ RfTf[ R^TjTj[ RhNlR RbEgElI R_O[S R_F\\J',
	    'PoSDZD R]FlF RRH[H R^IkI RSKZK R\\LlL RSOZO R^SjS R\\WlW RSZZZ Rf[j[ RSSS[ RSSZSZZ R^O^[ RaCaL RdLdV RhChL R^OjOj[',
	    'PoSDZD R]DlD RRH[H RSKZK R`MkM RSOZO R`QkQ RSYZY Rb[e[ RSSS[ RSSZSZY R`I`Q ReQe[ R`IkIkQ RiTlY ReEdI RaT_Y R]D]R\\Z',
	    'PoSDYD RRHZH R\\JbJ RdKlK RSKYK R\\NbN RSOYO RSYYY R_[b[ RSSS[ RSSYSYY R\\F\\S R_C^FbFc[ RcQ[Y ReNgT RbR[S RjDj[f[',
	    'PoSDZD R]FlF RRH[H RSKZK R_MiM RSOZO RaXgX RSYZY RSSS[ RSSZSZY R]P][ R_I_M RaSaY RdCdF RaSgSgX R_IiIiN R]PlPk[h[',
	    'PoSD[D RRH\\H RSK[K RSO[O R^TcT RfTkT RSZ[Z R^ZkZ RSSS[ RSS[S[Y R^O^[ RdGdK RfOkOk[ RiFkK RcM^O R_E]L RiC_E',
	    'PoSDZD RRH[H R]IaJ RSKZK R`PkP RSOZO R\\TlT RSZZZ R^ZkZ RSSS[ RSSZSZY R^U^[ RdOdZ RkUk[ RaN^Q R`GeI RaFjFeK\\O RcC`G\\H',
	    'PoSDZD RRH[H RSKZK R]MlM RSOZO R\\RlR RSZZZ R^ZkZ RSSS[ RSSZSZZ R^T^[ RdMdZ RkTk[ R^H_K RcGdK RkGhL RjD^E',
	    'Po^GkG RRH[H R^JjJ RSKZK R^MjM RSOZO R^PkP RSZZZ RSSS[ RSSZSZY R_F_Q ReFeP RVCWF RdCeE R^ScWj[ R_C\\J R\\SjSfWaZ\\[',
	    'PoSDZD R]DkD RRHZH R_JbJ RfJiJ RSKZK R]MbM RfMjM RSOZO R^PjP R\\SlS RSZZZ Rc[h[ RSSS[ RSSZSZY R\\G\\J RdDdN R\\GlGlJ R_S_VjVh[',
	    'Po]DlD RRH\\H RSKZK R^MkM RSOZO R^QjQ R]TlT RSYZY Ra[d[ RSSS[ RSSZSZY R^H^M RbDbM RdTd[ RgDgM R^HkHkM RVCWF RhVlZ R`V]Z',
	    'PoRH[H RSLZL RSOZO R^RkR RSZ[Z Ra[i[ RSSS[ RSS[S[Z R^F^Q RbUb[ RiXh[ R^FkFkQ RdSfV RVCWF ReLhO RcCcF RcH`K RiTlY R^U]Z RbIhJdNaO',
	    'PoRE_E RSG^G RhGlG R`IjI RTMZNgO RRQlQ RWSgS RWUgU RVZgZ RVWV[ RYCYG RYIYL RTI^I^L RVWgWh[ RbDhDhG RbDaG RbKgMlN RTISO RhK`M',
	    'Po]FlF RRH[H RSKZK R_NjN RSOZO RSYZY RSSS[ RSSZSZY R^R^[ R_H_N RjHjN RUCVF ReJaL RgHdK ReCeF RbHhK ReNaW R^RkRk[h[ RgThWaW',
	    'PoSDZD R\\FlF RRH[H RSK[K R^NjN RSO[O R^QjQ R\\UlU RSY[Y RSSS[ RSS[S[Y R^J^Q RaCaH RgCgH R^JjJjQ RdSeWhZl[ RcW`Y\\[ RdQbX',
	    'PoRElE RVJhJ RVMhM RROlO RWQgQ RWSgS RWVgV RWZgZ RVHVM RWXW[ RZCZG RdCdG RWXgXh[ RVHhHhM RbOkS R[ORS',
	    'Po]FlF RRH[H RSKZK RSOZO RbWgW RTYZY Rh[k[ RTST[ RTSZSZY R^L^[ RbRbY RbRgRgW R^LlLl[ RUCWF RfNiQ RcDeF RcN`Q R`HaK RhHfL',
	    'PoSDZD R\\FlF RRH[H RSKZK R_OiO RSOZO RaXhX RSYZY Rg[k[ RSSS[ RSSZSZY R]K][ RaSaY RdCdF RdKdS RaShShX R]KkKk[ R`GaJ RhGgJ',
	    'PoTDYD RRH[H RSKZK RSOZO RSYZY RSSS[ RSSZSZY R\\DbDbL ReDkDkL RhFdH R_F]H R`I\\M RdMgPlR RhJdL RcM_Q[S ReQ^T RgT_W RiWdY][',
	    'PoSDYD R[FlF RRHZH RSKYK RSOYO R`QbQ RhQkQ RSYYY Re[i[ RSSS[ RSSYSYY R\\I\\Q R\\IbIbQ RfIfO RkHkQ R_C`E RhDgF R^KaK R^NaN RcTbX`Y[[ R[TkTj[',
	    'Po]EkE RRHZH R]HjH R\\KlK RSKYK RSOYO R^QjQ R^TjT R^WjW RSYZY RSSS[ RSSZSZY R^N^W RdCdK R^NjNjW RUCVF RgXl[ RaX[[',
	    'PocFjF RRH[H R`KiK RSKZK RcNjN RSOZO R`SiS R`WiW RSYZY R]ZlZ RSSS[ RSSZSZY R`P`Z RcIcN RcCcH R`PiPiZ RUCWF R]HlHkJ R]H]R[[',
	    'PoSDYD R`FkF RRHZH RSKYK RaMjM RSOYO RaQjQ R_UlU RSZYZ Ra[l[ RSSS[ RSSYSYZ RaJaQ ReCeY RaJjJjQ R[J]M R[D^G R[Q]P]XZ[ R]QZP',
	    'PoRH[H R\\HlH R^KjK RSKYK RSOYO R]QkQ R\\TlT RSYZY RSSS[ RSSZSZY R]N]Q R^E^K R^EjEjK R]NkNkQ RVCWF RdCcQaT_XlZ RhUeY\\[',
	    'Po]GiG RRHYH RSKXK RSOXO RSZXZ RSSS[ RSSXSXZ ReLjKkM RfDjH RaC]G R]H\\L RUDUG RcMlQ RfHeL RbN[Q RcQ]T RfS^W RiV_[ RaJaL[L',
	    'PoSDZD R]DlD RRHZH RSKZK RbMiM RSOZO R_VdV RgVkV R]YlY RSYZY RSSS[ RSSZSZY R]D][ R_P_V RbHbM R_PdPdV RgPgV RbHiHiM RgPkPkV',
	    'Po\\GlG RRH[H R^KjK RSKYK RSOYO R^PjP R^SjS R^WjW RSYYY RSRS[ RSRYRYY R^M^V R_D_J ReDeJ R_DjDjJ R^MjMjV RVCVF RgXlZ RbX\\[',
	    'PoRH\\H RSK[K RSO[O R_UkU RSY[Y R_ZkZ RSSS[ RSS[S[Y R_Q_[ R]DcDcM RdNdQ R_QkQk[ ReDlDlM RVDXF R]GaH RfGiH RcJ]L RlIfL',
	    'Po^GlG RRH[H RSKYK R_MlM RSOYO RcSiS RSYZY RSSS[ RSSZSZY RcIcS RcVcZ RdCeF RiIiS RUCVF RfVgZ RjVlZ R`V_[ R^G]S\\Z',
	    'PoTE^E RbGkG RRG_G RSI^I RWOgO RRQlQ RWSgS RWZgZ RWXW[ RYCYI RWWhWh[ RWKSN RcDaGfKlN RiGfK_N RVL]K]NZM RdUgU RYUWU RfUbUeU RXU[U^UbU R\\UYU RcU_U\\U',
	    'PoZGcG RRHYH R[KcK RSKXK RSOXO RZOcO R]SbS RSZXZ RSSS[ RSSXSXY RhVc[ RUDUF RfRjZ Ra[^[ RgCdLgV R_C^O\\Y RbTa[ ReIkIjMhV',
	    'PoSDZD R\\FlF RRH[H RSKZK RSOZO R^OkO R]RkR R^VkV RSZZZ R\\[l[ RSSS[ RSSZSZZ R^J^O RaCaI RdJd[ RhChI R^JkJkO',
	    'PoTE^E RRG_G RTJ^J RSM_M RRPlP RWSgS RWUgU RVZgZ RVWV[ RYCYG RYJYO RVWgWg[ RaFiFjM RlKlN R^N`P RdJ`N RaHgK RdDcL',
	    'PoeGkG RRHYH R[KaK RSKXK RSOXO RSZXZ RSSS[ RSSXSXY R_E_[ RkHeM RhNbT RkSeY RgDbI RUCVG R^KaR RfPlPiV ReJgLbN ReTgW`[ RaD[E R^J[T R^H]P',
	    'PoSDYD RcEjE RRHZH RbKiK RSKYK R`KfK RcNkN RSOYO R^VlV RSYYY Ra[e[ RSSS[ RSSYSYY R_QeQe[ R`RaT RiRhT R]HlHkK R]H]S[Z RdCdL^K RiPcP',
	    'Po_GiG RTH[H RUPZP R]PkP RRRlR RVThT RVZhZ RVXV[ RTHSLZLZP RSD[D[H R_D_G RdGdO RVXhXh[ R_DiDiG R^IjIjM R^I^MiMkP ReVhV ReVcVeV RXVVV RYV[VYV R\\V_VbV_V\\V',
	    'PoSDZD R^GjG RRH[H R^JjJ RSKZK RSOZO R]QkQ RSYZY RSSS[ RSSZSZY R]M]Q R^D^J R^DjDjJ R]MkMkQ RaMaQ RfMfQ R_TbXfZl[ R\\TjTgWcY\\[',
	    'Po\\GdG RRHZH RfKlK RSKYK R\\NcN RSOYO RfRlR R\\RcR R[VdV RSZYZ Rh[l[ RSSS[ RSSYSYY R\\J\\R R`C`J R`R`[ R\\JcJdR RfElEl[ RUCVG RfEfSeZ',
	    'PoRHYH RSKXK R\\NbN RSOXO R\\RbR RSZYZ RSSS[ RSSYSYY R\\I\\X RZIcIb[ RgWd[ RUDUF RgRjZ RiQfZ R[EbE`I RfJgV RfJkJhV RfCdO RcWZX',
	    'PoSDZD R\\FlF RRH[H RSKZK R\\OlO RSOZO R]SlS R\\WlW RSZZZ RSSS[ RSSZSZY R_K_S RaCaI RdKd[ RhChI R]KjKiS',
	    'PoTDYD RRH[H R]IbI ReIkI RSLZL RSOZO R]OkO R]SkS R\\WlW RSYZY RSSS[ RSSZSZY R]D]I R]L]S R]DbDbI RdLd[ ReDeI R]LkLkS ReDkDkI',
	    'PoSDZD RcFkF RRHZH RSKZK RaLhL RSOZO RcOkO RfWkW RSZZZ R][l[ RSSS[ RSSZSZZ R_R_W RcJcO RcQcZ RcCcH RfQfZ RkRkW R]IkIkK RcV_W R]H]S[Z RdL_L',
	    'PoSDZD RRH[H RSKZK RSOZO R]OjO R^ViV RSZZZ R^ZiZ RSSS[ RSSZSZY R]H]O R^R^[ RcIcO R^RiRi[ R\\JkIkO R_K`N RaEgElJ R_F[J RgKfM',
	    'PoSDYD RRHZH RSKYK RSOYO RSZYZ Rb[l[ RSSS[ RSSYSYZ R\\R\\Z R_P_Y R\\DbDbM RdRd[ RhPh[ RdGeI R\\G\\I RaM_M RhDeM RdDkDkMgM R_TbT_T RkThTkT R_D]M RbYZZ',
	    'PoSDZD R]ElE RRH[H R^IkI RSKZK RSOZO R^OjO R\\SlS RSYZY R^ZjZ RSSS[ RSSZSZY R^V^[ R^K^O RdCdI R^KjKjO R^VjVj[ RaOaS RgPfS',
	    'Po\\FkF RRH[H R_KgK RSKZK R\\OlO RSOZO R_ViV RSYYY R_ZiZ RSSS[ RSSYSYY R_R_[ RcHcO R_SiRi[ RUCVF RfFjK RcE]K RiKeP[T',
	    'PoSDXD R[GcG RRHYH RSKYK R\\NbN RSOYO RSYYY R\\[_[ RSSS[ RSSYSYY R\\J\\N R_T_[ R\\JbJbN R[QcQ_T RgWc[ RgRkZ RiQfY R_C_G RfNiX RfKhW RfJkJhV RgCdO RbU\\V',
	    'PoSDZD RRH[H RSKZK R_LhL RSOZO R^TiT RSZZZ R\\[l[ RSSS[ RSSZSZZ R^O^T R^OiOiT RgHlM RiDfG RkGhJ R]G`I[M R`UaY ReChJ RgUfY R]EbE_K',
	    'Po^FiF RRHZH R\\IkI RSKYK R\\MbM RdMkM RSOYO RdQlQ R\\TlT RSYYY Rf[l[ RSSS[ RSSYSYY R_K_Q RdCdI RfTf[ RhKhQ RlXl[ RUCVF R_Y[[ RaT`Y RbP[Q',
	    'PoRHZH R\\HbH RfHlH RSKYK R\\LbL RfLlL RSOYO RaRgR RaVgV RSZYZ RdZgZ Ri[l[ RSSS[ RSSYSYY R\\E\\[ RaOaX R\\EbEbL RfEfL RaOgOgY RfElEl[ RVDVG',
	    'PoSDZD R_EeE RRHZH RSKZK RSOZO RSYZY Rg[k[ RSSS[ RSSZSZY RcVcZ RdFdJ RhUiW RhFlI ReVfY R^E\\J R`V^Z R^OjOiS R^KhKhO R^K^TlTk[ R^N\\Y RiC`D',
	    'PoTElE R_KjK R_MjM R_OkO RZQiQ RVSlS RZTiT RYZiZ RYXY[ RZHZO R_H_O R`C`F RfGfO RYXjXj[ R\\GVL R`G\\K RdVgV RaVcV R]V`V RTETSSZ R_V\\V ReVcV',
	    'PoSDZD RRH[H RSKZK RSOZO RfSkS RaYgY RSZZZ Rh[k[ RSSS[ RSSZSZY R]O][ RaUaZ RfOfS RaUgUhY R]OkOk[ R]IlIiL R_EjEdG RcJ]M RcObR_S RbFjI ReIeNbN',
	    'PoSDYD RRHZH RSKYK R[NbN RSOYO R[QlQ RSZYZ RSSS[ RSSYSYY R[G^J RaKbN ReGhJfMkLlN RgOjP R^UbV RjFhK R^D\\H R`F^K RhDfH RiTdY`[ RkWk[i[ R^Q\\Y RdCdKfX',
	    'Po]GkG RRH[H RSLZL RfMjM RSOZO R^PjP R^RjR R\\UlU RSZZZ Rc[g[ RSSS[ RSSZSZY R^J^R RgFgM RgRg[ R^JjJjR R_WaY RVCWF R`DbF RbGbL`N RhCfH',
	    'PoSDZD RRH[H R\\HlH RSKZK R`OhO RSOZO RaXgX RSZZZ Rh[k[ RSSS[ RSSZSZY R]Q][ RaJaO RaTaZ RaTgTgX R]QkQk[ RfHkM RbH\\M R]EdE_KhKhN RiDbE',
	    'PoSDZD RRH[H R\\JkJ RSKZK RSOZO R^UjU RSZZZ R^ZjZ RSSS[ RSSZSZY R^Q^[ R^EdEdP RdQdZ R^QjQj[ R_F`I RiFgI RdI^O[P RdIiNlP RiCaD',
	    'PoTEYE R\\FlF RRH[H RSLZL R^LjL RSOZO R^OjO R\\SkS RSYZY RSSS[ RSSZSZY R^I^O RdCdS RaVa[gZh[ R^IjIjO RiVlY RhQlT RbTeU R^V\\Z',
	    'PoSDZD RRH[H R]HcH ReHkH RSKZK R]LcL RSOZO R]QlQ R\\VlV RSYZY RSSS[ RSSZSZY R]D]L RaNaV R]DcDcH RgNgV ReDkDkH RhXkY RfDfLlKlL R`Y[[',
	    'PoRHZH RSKYK RZMlM RSOYO R[TlT RSZYZ RSSS[ RSSYSYY R^F^T R`Wa[ RaGaT ReGeT R]HjGiT RhWkZ RVDUG RdWf[ R^C[J R\\WZ[',
	    'PoSDZD R\\FcF ReFlF RRHZH R\\KcK RdKlK RSKZK RSOZO R^VjV RSYZY R^ZjZ RSSS[ RSSZSZY R\\H\\K R^R^[ R`C`L`O ReHdK R^RjRj[ R_M[Q RcN_O RfOcP RhPlOlP RhChKhP R`F_N RhFgN',
	    'Po]FbF ReFlF RRHZH RSKYK RSOYO RSYYY R^ZiZ RSRS[ RSRYRYY R^S^[ R^SiSi[ RUCVF RcJgN R^E`H RgFhI RcJ^OfNkP R^C\\H RfCcI R_N[P',
	    'PoSDYD R[GdG RRHZH RSKYK RZMkM RSOYO R\\TcT R\\XcX RSYYY RSSS[ RSSYSYY R\\P\\Y R]H]L R`C`G R\\PcPcW RhDkG RhUb[ RjOfW RbHbL RlWk[i[ RgCfQhZ',
	    'PoSDYD R]HkH RRH[H RSKYK R^LkL RSOYO R^OkO R^TlT RSZZZ RSSS[ RSSZSZY R^G^S RaWa[ ReFeS ReVgZ RiVl[ R_C\\J R]V[[ ReDdL',
	    'PoSDZD R\\DlD RRH[H RSK[K R]LkL RSO[O R^QjQ R^TjT R\\WlW RSY[Y RSSS[ RSS[S[Y R]H]L R^N^T RbDbL RdTd[ RfDfL R^NjNjT R]HkHkL',
	    'PoSDZD R\\FcF ReFlF RRHZH R\\JcJ RdJlJ RSKZK RSOZO R^UjU RSYZY R^ZjZ RSSS[ RSSZSZY R^Q^[ R^QjQj[ R`C_KbN RhChJjN R]O[P ReNcO R`F_N',
	    'PoSDZD R]GlG RRH[H RSKZK R\\OlO RSOZO R_VjV RSZZZ R_ZjZ RSSS[ RSSZSZY R_R_[ RbGbO RfGfO R_RjRj[ RhCgF R^I_M R_CaG RjIiM',
	    'PoRHZH R\\IaI ReIjI RSLYL R\\MaM RSOYO R\\RaR ReRkR R\\WaW ReWkW RSZYZ Rb[l[ RSSS[ RSSYSYZ R\\M\\W R\\E\\I RbEb[ ReEe[ R\\EjEjI ReMkMkW RlXl[ RUCVF',
	    'PoSDYD RRHZH R]IkI RSKYK R\\LlL RSOYO R^SjS RSYYY RSSS[ RSSYSZY R]D]H R^N^S RaUaZ RaDaH R^NjNjS R]DkDkH RdSeWhZl[ ReSaT[W RkUfW RfDfI ReY]Z',
	    'PoSDYD RRHZH R_IhI RSKZK RSOZO R\\PbP RePkP R\\SkS RSYZY RSSS[ RSSZSZY R\\K\\P R_D_I R\\KbKbP RdQd[ ReKeP R_DhDhI ReKkKkP RdSjYlY RcS^Y[Y',
	    'Po\\FlF RRHZH RSLYL R`MgM R]OjO RSOYO R^UjU R^XjX RSZYZ R[ZlZ RSSS[ RSSYSYZ R]H]O R^R^X R`K`M R`JgKgM R^RjRjX R]HjHjO RVCWF RbDdF',
	    'PoRH[H RSKYK R_OiO RSOYO R]VkV RSYZY R]ZkZ RSSS[ RSSZSZY R\\H\\K R]R][ R_K_O RdCdG RdRdZ R_KiKiO R]RkRk[ R\\HlHlK RUCVF R^D_F RiDhF',
	    'PoRHZH R`HlH RSKYK RSOYO RSZYZ Ra[l[ RSSS[ RSSYSYZ RhPhX RfOkT RbCcG ReH`M R[M]M]XZ[ RVDVG R\\E]I R]Xc[ RiCgH ReMcP_Q RjKgOcU_V RfYcY RfMgV',
	    'PoSDYD RRHZH R_JgJ RSKYK RSOYO R]SbS ReSjS RSYYY RSSS[ RSSYSYY R]M]S R]MbMbS ReMeS ReMjMjS RhXk[ R_WaY RcCiIlJ RfWc[ RcC\\J RgUeZ R_T]Z',
	    'PoRHYH R[JbJ RSKXK R[NbN RSOXO R[QcQ RSZXZ RSSS[ RSSXSXY R[F[N R^C]FbFbN RUDUF RgRjZ RiQeZ Ra[^[ RfMiX ReJhV ReJkJhV R^Q\\Y RfCdO R]UbUa[',
	    'PobFlF RTG^G RTI^I RRK_K RYO]O RRQlQ RWSgS RWUgU RWZgZ RTDTH RWWW[ RVM]M\\O RTD^D^H RWWhWh[ RWLTO RcHgMlN RcCaH RiHeL`N RXIYK',
	    'PoRE_E RbFlF RTLZL RWNgN RRQlQ RWSgS RWUgU RWZhZ RTITL RWWW[ RTIZIZL RWWhWh[ RUFRI RcE`H RbFfKlM RiGfJ`M RTH^H]M RVCWF R]C\\E',
	    'Po]FlF RRH[H R_KlK RSKZK RSOZO R^QlQ R`TjT RSYZY RSSS[ RSSZSZY RcIc[ RgJg[ R`MjMjT RVCWF RfTlY RdCdF RcT^Y R]F]U\\Z',
	    'PoRH[H R]HjH RSKZK R]LjL R]NkN RSOZO R_QjQ R]TkT RSYZY RSRS[ RSRZRZY R]E]L R`T`[ RbCbL RfCfL R]EjEkL RVCWF ReUhYl[ RjUfW RdZ_Z R]N]U\\Z',
	    'PoSDYD R]FkF RRH[H R\\JlJ RSKZK RSOZO R^PjP R^SjS RSZZZ RSSS[ RSSZSZZ R^M^S RdCdF RaVa[gZh[ R^MjMjS RbUfV RiWlY R`G`J R]W\\[ RgGgI',
	    'Po\\FlF RRH[H RSKZK R^LjL RSOZO R^OjO RSYYY R^YiY RSSS[ RSSYSZY R^I^O R^S^Y RaCaG RgCgG R^IjIjO RdTgW RVCWF ReSaW RaO\\T Rj[g[ R_RkSj[',
	    'PoSDZD RRHZH R\\IlI RSKZK RgLkL RaNjN RSOZO R]PlP R`SjS R`UjU RSYZY R`ZkZ RSSS[ RSSZSZY R`W`[ RgIgL R`WkWk[ R`EfEeH RcIbL_L R`E\\I]R\\Z',
	    'Po`EkE RTG]G R_IlI R`KkK RUM]M RWOgO RRQlQ RWSgS RWUgU RWZgZ RWWW[ RTD]D]G RUJ]J]M ReCeF ReIeM RWWhWh[ RbEcI RhEhI RTFSN',
	    'PoSDXD RRHYH RSKYK RiMlM R[NbN RSOYO R[SbS RSZYZ RSSS[ RSSYSYY R[E[M R^O^X RbEbM ReDiDjL RlJlM RfVk[ RhUc[ R]C[F RdPhX RcOjPgX R[I]I[I RbI_I RbE_E RbXZY ReEdM',
	    'PoRH[H RSKZK RSOZO R]QjQ R^WiW RSYZY R^ZiZ RSRS[ RSRZRZY R]K]Q R^S^[ RdKdQ R^SjSj[ R]KjKkQ RdChG RVCWF R`M`O R`IgHlI RgMgO RdC_H[I',
	    'PoSDZD RRH[H R]IkI RSKZK R]LkL RSOZO R\\PlP R]TlT R]WkW RSZZZ RSSS[ RSSZSZY R]D]I RbDbI RdIdO RdTd[ RfDfI R]DkDkI R`QaT RhQgT',
	    'PoSDZD R\\FkF RRHZH R]IjI RSKZK R[MlM RSOZO R[SlS RSYZY R][`[ RSRS[ RSRZRZY R`O`[ RdFdM RiOkQ R_C`F RhCgF RjUfYcZ RcO\\O RdV[W RfOgYk[lY',
	    'PoSFkF RUIiI RRLlL RTN[N RcNjN RRQ]Q RaQlQ RTS[S RbSjS RTU[U RbUjU RS[\\[ Rb[k[ RSWS[ RSW\\W\\[ R_F_[ RbWb[ RbWkWk[ RXC[F ReDcF',
	    'PoSDYD R\\FlF RRHZH RSKZK R]MkM RSOZO R]PjP R[SlS RSZZZ RSSS[ RSSZSZY R`U`Z RaKaS RdCdF RgKgS RdSeWhZl[ RfHlJ RaH\\J RcT[W RkUfW ReZ][',
	    'Po]EkE RRH[H R^JbJ RfJjJ RSKZK R^MbM RfMjM RSOZO R\\PlP RSYZY Ri[k[ RSSS[ RSSZSZY R\\H\\K R]T][ RcSc[ RdEdN RgTg[ R]TlTl[ R\\HlHlK RVCWF RcPbV',
	    'PoSDYD RRHZH RaIkI R_KlK RSKYK RSOYO RbQjQ RbXkX RSZYZ Rb[l[ RSSS[ RSSYSYZ RbNbX RfCfK RbNjNjR RaEkEkI RbTkTkX R[K^M R\\D_G R[Q^Q^X[[ RaEaI',
	    'PoRHZH ReIlI R[JdJ RSKYK RSOYO R[OdO R\\TdT RSYYY Rg[j[ RSSS[ RSSYSYY R]K^N R_D_J R`O`Y RaDaJ RjDj[ RUCVF R\\E\\H RdEdH RbKaO RgOgU RdX[Y',
	    'PoXDgD RSFkF RWNgN RTP\\P RaPjP RRR^R R`RlR RTZ]Z RaZjZ RTXT[ RTX]X][ RaXa[ RWLWMgLgN RaXjXj[ RWHfIgJ RaTiUjV RTT[U]V RZJWJ RVVTV RdVaV RbJeJ R^JaJ RXJ[J^J RbVeVhV RUVWVZV R\\JYJ R`J]J RfJcJ`J R[VYVVV RiVfVdV',
	    'PoSDZD R\\ElE RRHZH R]JkJ RSKZK R^MjM RSOZO R^OjO R^RlR RSYZY RSSS[ RSSZSZY R_I_R RaCaG RdIdR RgCgG R_H[L R_TbWfYl[ R\\TjTgWcY\\[',
	    'PoSDYD R\\EkE RRHZH R]HjH RSKYK R^MiM RSOYO R]PjP R\\RlR RcUlU R]YbY RSZYZ Rf[i[ RSSS[ RSSYSYY R]T]Z R]TbTbY RdCdH RcMcP RiSi[ RdWfY R\\KlKkM',
	    'PoSDYD R\\ElE RRHZH R^KiK RSKZK RSOZO R^PiP RSYYY RSSS[ RSSYSYY R\\M\\P R^H_K RdCdF R^HiHiK R\\MkMkP RfTkY RdPgW RdP\\T RbT\\W RkSfU RcW[Z RcSeWd[a[',
	    'PoRHYH RSKYK R\\OaO RSOYO RgSkS RZSbS RSZYZ RSSS[ RSSYSYY R\\C\\JbJbK RcMkMkP R^UaW ReGhK RcElEgI RUDVF RdTfY R\\L[R RaE\\H RhLgZlZ RdQbZ R^P]W',
	    'PoTG\\G RaGjG RTI\\I RaIjI RTL\\L RaLjL RRPlP RWRgR RWUgU RWZgZ RTDTL RWWW[ RTD\\D\\L RaDaL RWWhWh[ RaDjDjL R^N`P RZM[O RhMkO RdMbO RVMSO',
	    'PoTD^D RaFlF RbGjG RTJ]J RTL^L RaLkL RWOgO RRQlQ RWSgS RWUgU RVZhZ RTDTL RWWV[ RXEYG RXJXM RTG]G]J RaIaM RdIdM RgIgM RVWhWh[ RaIkIkM RbC`H',
	    'Po]EbE RVGZG RdGiG RVIZI RdIhI RRKlK RZNdN RWSgS RWUgU RWZgZ RVDVK RWWW[ R]I]L RWWgWg[ RdDiDiK RdKiP RVPhPlQ RZKUPRQ RZCUD RaGaL R]C]GaG',
	    'PoSDZD R\\FlF RRH[H RSKZK RSOZO RSYZY R_[b[ RSSS[ RSSZSZY R]H]P R\\QcQc[ RfHfM ReQkQk[ R`CbF R`Q\\U RaU\\Z RiQeU RjUeZ RhCfF R_JbJ R_MaM RkHjOhO Rh[k[h[ R]HcHcOaO',
	    'PoTEYE R\\EcE ReEkE R]HbH ReHkH RRH[H RdJlJ RSLZL RSOZO R[PlP R^SiS RSYZY RSSS[ RSSZSZY R`C`L RhChL RaVa[gZg[ R^MiMiS RbTeW RiVlZ R]W\\Z RcJ\\K',
	    'PoSDZD R\\ElE R]HkH RRHZH RSKZK RfLkL R]NkN RSOZO R]RjR R]UjU R]WjW RSYZY RSSS[ RSSZSZY R]J]N R]P]W RdCdG ReJfL R]PjPjW R]JkJkN RgXl[ RcJaL^L RaY[[',
	    'PoSDYD R`FlF RRHZH RSKYK RbNiN RSOYO RcUhU RiXkX RSZYZ Ra[l[ RSSS[ RSSYSYZ R`K`X RcQcV RfCfF RfLdQhQhU R`KkKkX R[J]M R[D^G R[Q]P]XZ[ RcGcJ RhGhK R]QZP',
	    'PoRHYH RSKYK R]NjN RSOYO R]QjQ R]SjS RSYYY RSSS[ RSSYSYY R]L]S R]LjLjS R]F\\IkHkK RTCWF R`CZI R^EfEbJ^J RbS`U\\W RbIhJ R_WdYk[ R^ViVcY[[',
	    'PoSDZD R\\FcF ReFkF RRHZH R\\IcI RdIlI RSKZK RSOZO R^QjQ R^TjT R^WjW RSYZY RSSS[ RSSZSZY R^N^W R^NjNjW R`C_JbL RhJlM RgXl[ R^M[N RfLdM RaY[[ RhDgK',
	    'PoSDYD RRHZH R^IjI RSKYK R^MjM RSOYO R\\UbU ReUlU RSZYZ R\\ZbZ ReZlZ RSSS[ RSSYSYZ R\\P\\[ R^E^M R_P_Z R\\PbPb[ RdEdM RePe[ RiPiZ R^EjEjM RePlPl[',
	    'PoSDZD RRH[H RSKZK R\\NlN RSOZO R^WjW RSZZZ R^ZjZ RSSS[ RSSZSZY R\\F\\I R^S^[ RdCdF RdIdR RdSdZ R^SjSj[ R\\FkFkI RhJgM R_J`M ReNhQlS RdM_Q\\S RhH`I',
	    'PofDkD RYGdG ReHlH RXLdL RYNdN RRQlQ RVShS RVUhU RUZiZ RUXU[ RWHWO RZLYN R\\C\\G R_J_O RZDcDcG RUXhXi[ RWGTJ RWESF R\\J`J RiHiOfO RZJ\\IcI_J',
	    'Po\\HcH ReHlH RRHZH R\\KcK ReKlK RSKYK RSOYO R`UcU R`YhY RSZYZ Ri[l[ RSSS[ RSSYSYY R\\D\\[ RaSaZ R\\DcDcK ReDeK ReRhRhY ReDlDl[ RbM_Q RbNgNfQ RUDVF RcR`S RhUeU',
	    'Po]ElE RRHZH ReJlJ RSKYK RSOYO ReUkU RSZYZ R][l[ RSSS[ RSSYSYY R`T`Z RbGbQ ReReZ RiGiQ R^JbJdN RhIfO RUCVF RhIkP RaJ_P RcG^G RjGeG R]E]R[Z RaGaM',
	    'PoSDYD RRHZH RaHkH R`KlK RSKYK RSOYO RbPjP RbRjR RbUjU RSZYZ Rb[l[ RSSS[ RSSYSYZ RaEaH RbMbU RfCfK RbMjMjU RaEjEjH R\\K^M RhVkY R\\D_G R[Q^Q^X[[ RcVaX',
	    'Po\\DaD R[FcF R\\IbI R\\KbK RRLZL R\\QaQ RXTkT RVKVR R\\M\\Q R\\MaMaQ RgKgR RXI[L RSFVI RdFfI RcLjJlL RjFdK RVDTF RXFSK RgDdF RYNYP RjNlQ RSNRR RdNcQ RYTSW ReU_XWZ R[QWU^XbYl[ RaXR[',
	    'PoaDkD RSE^E RRH_H RWQgQ RRRlR RV[h[ RVXV[ RTI]I]O RVXhXh[ RUEVH R[EZH RTITN\\L]KZK RaCaFjGfH RdHgH RdHaIhJiK RTKXKZK RWTfUgV ReKcK RhKeK RYVWV RdKgK ReVbVeV RYV[V^VaV RaHaNeNkNkO R\\VYV RcV`V]V',
	    'PoSDYD R[FlF RRHZH R`IgI RSKYK RSOYO RaTeT RhTlT RSYYY RSSS[ RSSYSYY R^K^T R`C`I RaMaT RaWb[ RaMeMeT RgCgI RiKiT RlRlT RiWkZ RkMhP ReWf[ R^W\\[ R^R[S R^N[N',
	    'PoSDYD R[ElE RRHZH RSLYL R\\MaM RfMjM RSOYO R[PkP R\\XaX RfXjX RSZYZ RZ[l[ RSSS[ RSSYSYZ R\\H\\M R\\R\\X R\\HaHaM R\\RaRaX RcEc[ RfHfM RfRfX RfHjHjM RfRjRjX',
	    'Po]EbE R\\HcH ReIlI RTI[I R]LbL RTNZN ReNkN R]ObO RTT[T ReTlT R]YbY RTZ[Z ReZlZ RUGU[ RYGYZ R]S][ R]SbSbY ReGe[ RiGiY RVCRN RfCcM RiDiK RYDXL',
	    'PofDlD RZDcD RYFeGlH RYKeK RZMdM RbOdO RgPiP RWQgQ RRSlS RV[h[ RVHVP RVXV[ RZMZO R^C]F R_K_P RVXhXg[ RiHiO RVCSG RWGRK RWUfVgV RYVWV ReVbVeV RYV[V^VaV RZJ\\IcI_J R\\VYV RcV`V]V',
	    'PoUH]H RaHjH RUJ]J RaJjJ RUL^L RaLlL RWOgO RRQlQ RWSgS RWUgU RWZhZ RUDUL RWWW[ RZDZL RbEbL RfDfL RWWhWh[ RUERH RbD_H',
	    'PoRHZH R_IhI RSKYK RSOYO RbPeP RgPjP R\\VjV RSZYZ Rh[j[ RSSS[ RSSYSZZ R\\R\\[ R\\L\\P RaRa[ RbLbP RfRf[ RgLjLjP R\\RjRk[ RUCVF RdElH RcC`G[H RbLeLfNgP R_L\\L R_L_P\\P',
	    'PoSDZD R_EfE RRH[H R^JjJ RSKZK R_LcL ReLjL RSOZO ReOlO R^VjV RSYZY Re[l[ RSSS[ RSSZSZY R^R^V RfJfO R]GjGjJ R\\SjSjW RgXjY RaO\\T R\\OaPgQdSbWaY\\[ ReWe[ RaC]G^N',
	    'PoSDZD R\\ElE RRHZH ReKjK RSKZK R\\NkN RSOZO R]QjQ R[TlT RSYZY RSSS[ RSSZSZY R`V`Z R]KaK`T R]HbHbK RdCcF RgKgT ReHjHjK ReUhZl[ RdT[W RkVfW R]H]K ReHeK RfY][',
	    'PoSDYD RRHZH RbIiI RSKYK RaLkL RSOYO RaQlQ RbTjT RSZYZ RSSS[ RSSYSYY R\\D\\[ RfIfL RdVd[hZh[ RbNjNjT RdUgV RjWlZ RiElH ReFfH RaD`I RaW_Z R\\E_E^K_S RiCbD R_U]U',
	    'PoSDYD R\\HcH RfHlH RRHZH R\\KcK RfKlK RSKYK R_NjN RSOYO R_UiU RSYYY RSSS[ RSSYSYY R\\D\\[ R\\DcDcK RdMdZ RfDfK RdTiY RdT_Y R_QiQiU R_Q_U RfDlDl[i[',
	    'PoSDYD RRHZH R[KlK RSKYK RSOYO R[Q_Q RbUeU RSYYY RSSS[ RSSYSYY R^K^X RbKbX R^F_H RcGeI RjEkH RhVc[ RkOgX RlWk[ R^D[J RcDaJ R[N^N[N ReQaR R[U^U[T ReNbNeN RhChRiZ ReW[Y',
	    'Po]GkG RRHZH RSKZK R^LkL R^OkO RSOZO R^QlQ RSYZY RaZhZ Ri[l[ RSSS[ RSSZSZY R]S][ R]D]G R_J_R RaWa[ ReIeQ RaWhWgZ RkDkG R]SlSl[ RUDWF RbU_V R`H\\M RfTjV RdCdGdI',
	    'Po]DkD RRHYH R_JhJ RSKXK R_LjL R\\MhM RSOXO R\\RaR ReRkR R\\UaU ReUkU RcYlY RSZXZ RSSS[ RSSXSYY R\\O\\X R_D_L R[ObOb[ ReOeY R_GhFhN RdOkOk[ RUDUG RcXZY',
	    'PoSDZD R\\ElE RRHZH RSKZK R]McM ReMkM RSOZO R^QkQ R^TkT R^WkW RSYZY R^ZlZ RSSS[ RSSZSZY R]I]M R_P_[ R`C`G R]IcIcM ReOeZ ReIeM RgCgG ReIkIkM R`O[S',
	    'PoSDZD R\\FbF ReFkF RRHZH RdIlI RSKZK RiMlM RSOZO R^QjQ R^TjT R^WjW RSYZY RSSS[ RSSZSZY R^O^W R^OjOjW R]D\\G ReDeG RgYl[ R`C_I]M RhCgIdM R\\IbIbL`L RaY\\[ RiIiM',
	    'PoZElE RRHYH R[IbI RSLXL RSOXO R[OaO R\\TiT RZWlW RSZXZ RSSS[ RSSXSXZ R^N^R R^H^K R_C_G RfCfG RdJjJkQlS RTCUF R\\[a[dZhYj[ RdMhQ R_W[[ R\\MZN RZKaLcM RfIfP RaRZR',
	    'PoSDZD RRH[H RSKZK R_LiL RSOZO R^RjR R]TkT R\\WlW RSYZY RSSS[ RSSZSZY R\\F\\J R^N^R R_I_L RbXb[ RdCdG RdNdW R_IiIiL R^NjNjR R\\FlFlJ RiXl[ R_D`F RiDgF R^X\\[ RfXf[',
	    'PoSDYD RRHZH R]HbH ReHjH R\\KkK RSKYK R^MdM RSOYO R^VcV RSYYY RSSS[ RSSYSYY R]D]H R^P^Y RaMaP R]DbDbH R\\PdPc[ ReDeH ReDjDjH RhWl[ RfQiY RjRfZ RfPkPhX RfLeT R\\K\\SZ[ RcS^S RcX]Y',
	    'PoSDXD R_EdE RRHYH RSKXK ReLkL R_LdL R\\NdN RSOXO R]ScS RaWdV RSYXY RSSS[ RSSXSXY R[H\\V\\[ R]P]S R_H_L R^XaX`[ RaTaW R]PcPcS R\\TdTd[ R[HeHdJ RjEkI RhChQgY R_E_J R_T^W\\W RhLjY R[QYZ RbJ\\J',
	    'PoSDXD RfDkD RZEcE RRHYH ReHlH R[HcH RSKXK RfKkK R`LcL R[OcO RSOXO RfOkO R[SbS R[UbU R[WbW RSYXY RfYkY RSSS[ RSSXSXY R[J[O R[Q[W R_C_H R`J`M R[QbQbW R[JcJcO RfSf[ RfSkSk[ R`Yd[ R^K\\M R]YY[',
	    'PoRL[L[Z RVE[G RaWY[',
	    'Po\\MlM RRLWLWY RdCd[ RUDYH R\\WVY',
	    'Po\\FlF R`[f[ RRLXLXY RfFf[ RTEXH R]VWY',
	    'PoRLYLYY RcCc[ RUDYI RhOlQ RcLhN R]VXY',
	    'PoRLXLXY RgVl[ RVDYG RaW\\[ RdPhX RcQ_Y R\\VWX RdCcMfV RcJbU',
	    'Pog[l[ RRLWLWY R_EgEh[ RlVl[ RUDYH R_E_T[[ R[WVY R_M]X',
	    'Po\\EkE R[NlN RRLWLWY RdEd[ RUDYH R\\WVY',
	    'Po]FkF R\\ZlZ RRMWMW[ RdFdZ RUDXH R[XVZ',
	    'Po\\JlJ Rb[g[ RRMWMWZ RhDh[ RUEYI R[XWY R`ObU',
	    'PocMjM R[[l[ RRMWMWZ RcCc[ RUDYH R\\WWZ',
	    'Po^YlY RRLWLWZ R^J^Y ReDeY RlJl[ RVDYH R[WVZ',
	    'Po^IlI R^NiN R][k[ RRLWLWZ RUDXH RgN]Y RlWk[ R`C\\L R[XVY',
	    'Poc[k[ RRLWLWY RcFc[ RUDYH R\\VWY RlWk[ RjD]G RgO]P RjN`O',
	    'PoRLWLWY ReEeX RlDl[ RUDYH R[WWY R_D^R]Y',
	    'PoRLWLWZ RUEXH RbEdI R]JaTfXl[ R[WVY RiJeS_XZ[ R\\E_Q RjEfQ',
	    'Po[OfO RRLWLWY R`E`[ RUDXG R[VVX RlVl[ R[EiEiUj[',
	    'Po^OjO R^[k[ RRLWLWZ R^O^[ R]EjEjP RUDYH RlWk[ R[XVZ',
	    'Pod[i[ RRLWLXY RVDYG RaU\\[ RcP`X R\\VVY R_J]Q R]FlFlNlQj[ RdFdKbU',
	    'Po[IlI RZQlQ RRLWLWY RgCg[ RUDXG R[WVY R`C_R^X',
	    'Po\\GlG R]LjL RRLWLWY RcCc[ RTDWG R\\WWY RhYkXhX R[QlQkX',
	    'Po]EkE R][l[ RRLWLWZ R]E][ RTDXH RcS_W R`IcLeOgSiW RhHcS R[XVZ',
	    'Po^ElE R^SjS R^[l[ RRLWLWZ R^E^[ R^KjKjS RUDXH R[XVZ',
	    'Po\\EkE R\\OlO Rb[f[ RRKWKWW RgEg[ RUDXG R^XZY RgN]X R[UWW R^G\\N',
	    'Pog[l[ RRLWLWY R]J][ R]JlJl[ RUDXG ReCdMgPiV RdM`U R[WVY ReGcR',
	    'Po^IkI R[QlQ RRLWLWY RdHd[ RUDXH R_D\\L R\\WWY',
	    'Poe[l[ RRLWLWZ R_H_[ RfDf[ RlWl[ RUDXG RZWVY RkHbQ R^JZN RaC\\M',
	    'Po_[j[ RRLWLWZ R_M_[ RUDYH RcEkM R[WWY RjXj[ RcCaG[M RhO_T',
	    'Po]YlY RRLWLWY R]H]Y RlHl[ RUDYG RcQ_U RaGbJdLfOiU RhFbS R[WVY',
	    'PoRMWMWY RUDXG RhJlM RgSiVk[ RfDjL R`D[N RbM]Z R[WVY ReY\\Z RgY_Y RjXbY',
	    'PoRLWLWY RlXl[ RUDXH RZVVY R`KfX ReK_X ReHbS R]EiEiUj[ R]E]R[Y',
	    'PogLlL RRLWLWZ R`DhDhL RUDXG R_J[N R^P_SdXk[ R[WUZ R\\PjPhSdW\\[ R`D_K',
	    'Po\\HlH Rc[h[ RRLWLWY RUDXG R[VVX RcDdG R`S\\Z RaHaPjNjTh[ RaK_W',
	    'Po[PlP RRLWLWY R]HiHiP RUDXG ReUk[ RaU\\Z RcDcPgX R[WWX RcLbU',
	    'Po\\FkF RdOkO R[[l[ RRLWLWZ R^M^[ RdFd[ RTDXH R[XVZ',
	    'PoZJlJ R]ZjZ RRMWMWY R]Q][ RcCcQ R]QjQj[ RUDXH R[WVY',
	    'Po]TeT Rd[i[ RRLWLWY R]J]U R]JeJeS R[EkEj[ RUDXG R[WWY',
	    'Po[EkE RZQlQ RRLWLWZ RcEc[ RTEXH RZXVZ R]H^N RiHgN',
	    'Po^LiL R^SiS R[[l[ RRLWLWZ R^E^[ R^EiEi[ RUDYH R[XWZ',
	    'Po^PkP RRLWLWY R^E^P R^EkEkP RUDXH RhTk[ R`T]Z R[WUY',
	    'PoaThT Rh[l[ RRLWLWY R]E][ RaKaU RaKhKhT R]ElEl[ RUDXG R[VVY',
	    'Po^IlI RbNlN RbTlT RRLWLWY RbHb[ RTDXH R`D[N R[WVY',
	    'Po^LlL RRLWLWY ReLe[ RUDYH RaQkU R[WWX RiD^F^R\\Y',
	    'PoRLWLWY RUDXG RcDiJlK RcD\\K RdKaM]O RfOcS^U R[WVY RjSgWbY][',
	    'Po]NlN RRLWLWX R]G][ RUDXG RYVVY RcWd[ RbZ\\[ RiD]F RlWk[i[ ReEdLfX',
	    'Po_SlS R^ZlZ RRLWLXY R^NlNl[ RUDYH RjIeN RaH]L RbFkFiK RcC`J R\\WWY',
	    'Po]IhI R^UfU Rg[l[ RRLXLXY R^N^W R^NfNfU R\\ElEl[ RUDXH R[WVY',
	    'Po^MlM R]ZlZ RRLWLWZ R]Q]Z R^F^M ReDeZ RlFlM RlQl[ RUDYH R[WWY',
	    'PofNjN R^ZlZ RRLWLWY R^Q^[ R^QlQl[ RUDXH RbJ]N RcE`L R[WVZ R\\ElEkN',
	    'PoRLVLWY RdCdN RUDXG RZVVX R_NaTfXl[ R]HlHkL R]NjNhTcX][ R]H]R[Y',
	    'Po]RjR R[VlV RRLWLWY RdOd[ RTDWH R^FcKjN R\\EjEeK\\N RZXUY',
	    'Po]ZiZ RRLWLWZ R]Q][ R]QiQi[ RUDYH R\\MdMjLkN RfHjL R_I[M RbC`I R[XVZ',
	    'Po\\ElE R_JjJ R_PjP R^WkW R\\[l[ RRMWMW[ R\\E\\[ ReJeV RUDXG R[YWZ',
	    'Po\\GjG R\\KjK R[PkP RRLWLWY RcCc[ RUDXH RdRkX RcNeS R[WWX RbR[Y RcNaT',
	    'Po[IlI R\\OcO RRLWLWY R_O_X RUDXG RhDkG R[WVY RcW[X RlWk[i[ RfDeMgY',
	    'Po\\HjH RZNkN R[TjT RZ[l[ RRLWLWZ RcCcN RcPc[ RUDXG RZWVY',
	    'Po]GkG R[MlM R[QlQ Rc[h[ RRLWLWZ RdCdL RhMh[ RUDXG R^TaW R[WVY',
	    'Po\\HlH R]NkN R^ZjZ RRLWLWY R^R^[ RdCdN R^RjRj[ RUDXG RYWWZ',
	    'PoZHlH RRLVLVZ RgVk[ RTDWH RdV_[ ReQhY RjMiS R_L[U RbMaS RZWUY R`D]Q RfJdV',
	    'Po\\HlH R^XaX RRLWLWY R\\NbNbW RUDXH RhDjF RZWVX RgVc[ RkNfX ReCdJfW R\\H\\S[[ RlWk[i[',
	    'Po^IjI R[OlO RRLWLWY RdCd[ RUDXG RgTlY R`T[Y ReOhV RbO_U RcMaT R[WVY R^D\\M',
	    'Po]IkI RZOlO Rf[k[ RRLWLWY RcCcO RfOf[ RlWk[ RUDXG R^WZ[ R[VVX R^D\\L R`O^X',
	    'Po\\LlL R]ZjZ RRLWLWY R]R][ RdEdR R]RjRj[ RTDWG R[WVY RiD[F',
	    'PofMkM RbVlV Rc[l[ RRLVLVY RbKbV RgEgV RTDWH RZVVX R]WY[ R^R[Z RZE_E[M_M]W RjDaE R[Q]V`YhZ',
	    'Po]EcE R]KlK R`YjY RRLWLWY R`P`[ R`PjPj[ RUDXH RZVVX R]E]S\\Y RjD`E',
	    'Po^MiM R]SiS R[[l[ RRLWLWZ RcMc[ RUDYH RcDkL RbE[M R[XVZ',
	    'Po\\KlK RfUiU Rb[k[ RRLWLWY RbNbZ RaNjNjU RUDXG RZWVY RlWkZ R^IZL R_FgFeJ R_C]J]S\\Z',
	    'Po^QfQ R^VfV Rd[j[ RRLWLWY R^L^W R^LfLfV RUDXG R`C\\K R[VVX R^HlHlRj[',
	    'Po^LkL R^TkT R^YkY RRLWLWY R^C^L R^P^[ R^PkPk[ RUDXH RkIlL R[WVY RkE]G',
	    'Po_FgF R[OlO R\\TjT R_[c[ RRLWLWY RcJc[ R[KjKjU RUDXG RfGcK R`FZK R`C^H R[WVY',
	    'Po[HlH R\\OfO RRLWLWZ RUDYH RiXl[ ReVhW RdH\\O RcCdF RhLbR\\U R[XVZ RkPeV[[',
	    'Po]IkI R^NjN R\\TlT RRLWLWY RdId[ RUDXH R_CaG R[WWX RiCgI',
	    'Poc[k[ RRLWLWZ R\\G\\K RcLc[ RcCeG R\\GlGlK RUDXG RlXl[ R[WVY RiK]M RfR\\S RjQ_S RlQbR',
	    'Po\\KkK R]QjQ R\\VlV RRLWLWZ R\\E\\I RdMd[ R\\EkElI RTDXH RZVVZ RbG^Q',
	    'Po][b[ Rg[l[ RRLVLVZ R[EbEb[ ReElEl[ RTDWH RlOgT R\\J^N RfJhN RbP^RZT RZXUZ',
	    'Po[GkG R\\KkK RZNlN R_SlS Rd[h[ RRLVLVY RiPi[ RUDVH RaUdX R]UZY RaN\\X RZVVX RbH^U RbC_S',
	    'PoZIlI RZQdQ RRLVLVY RaLaX RUDXG RhDjG RYXUZ RgUaZ RkLeW R]L]S[Y RfCeNgY RlWk[i[',
	    'Po[EkE R[[l[ RRMWMW[ RcEc[ RTEXH R^MaT RhMlU R[XVZ R^J[T RhKfU',
	    'Po[DkD RZOlO R]ZjZ RRLWLWY R]S][ R]SjSj[ RTEXH R[WVY RbD_O R\\IiIhO',
	    'Po^PkP R^UkU Rf[k[ RRLWLXY R^K^[ ReCeK R^KkKk[ RVDYG RlEhH R^E`I R[VVY',
	    'Po^JjJ R]OkO R[SlS RRLWLWY R^E^J R^EjEjJ RTDXH RcSeWgYl[ R[WVY RbV_Y[[ RdObW',
	    'Po]IkI R[NlN R^ZjZ RRMWMWY R^R^[ RdCdN R^RjRj[ RUDXH R^D[K R[WVY',
	    'Po\\IkI Rd[i[ RRLWLWX R\\EdEdO RUDXG RdHhMlN R_W[[ RcH^N[O R[VVX R\\QfQeTjUi[ R`Q_X RiD_E',
	    'Po^GkG R[QlQ R]XlX Rd[h[ RRLWLWY RUDXH RaSeU R[WWY RbMfO R_C\\K R_LjKjUi[ R_K]X',
	    'PobElE RbOlO Ra[l[ RRLVLVZ R_K_Y RgEg[ RTDWH R_NZS R_FYK RZWVY R^[[[ R[D^I_Q',
	    'Po\\PiP Re[l[ RRLWLWY R\\I\\P RfPfZ R\\IiIiP RlWl[ RUDXH R_W[[ R^C`G RhCeI R[VVX RaP_X',
	    'Po]ElE R]PlP R]UlU Rh[l[ RRLWLWY R]K][ RdKd[ R]KlKl[ RUDXH R`GiK RkFdH R[WUZ',
	    'Po\\IgI R]NkN RZSlS RRLWLWY RUDXG ReFjJ RbC\\J RcRdVgYl[ R^K\\P R[WVY RbU_YY[ RcNbV',
	    'Po\\FkF R]IjI R[LlL R^SiS R^WiW Re[i[ RRLWLWZ R^O^[ RdCdL R^OiOi[ RUDXG R[WWY',
	    'Po\\GgG R[LlL R_UjU R_ZjZ RRLWLWY R`O`[ RbCbL R^QjQj[ RTDXH RkEbNYR R[WVY',
	    'PodGkG R\\KbK R\\PbP RRLVLVY R\\E\\W RZEbEb[ RTDWG RYVUZ RgWc[ RgRk[ RjPeZ RkLgW RkHhV RdUZW ReGhV',
	    'Po[GlG R[NlN R^ZjZ RRLWLWY R_R_[ R`C`J RgCgJ R]SjSj[ RTDXH R`P[U RbJ^S RZXUY',
	    'Po]FjF R[TlT RRLWLWY RdCdJ RUDXH RhXl[ ReWiX R^MbO R\\P`Q R\\JkJiN R[WWX RdU`Y[[ RdNdV',
	    'Po[FlF RRLWLWY RfPfX RUEXH RZWVY RhMeN R^N`R RhPkX RaIZL RcLaO[R RdFaIeP`V[X RjJgMjV RdLfU Rd[`[',
	    'Po[HaH RfHlH R[NaN RfNlN R[SaS RfTlT RRLWLWY RaCa[ RfCf[ RUDXH R[VVX',
	    'Po\\IiI R\\NiN RZRlR RRLWLWY R\\D\\N RcDc[ R\\DiDiN RTDWG RcQkY RYWVY RcR]XYY',
	    'Po[IlI R[RlR RRLWLWY RdEdO RTEXH RcHgLlN RcH_LZN R[WVY R]WjZ RaP_T^V RgTcY[[ RiD_E',
	    'PofLjL R\\L`L R\\QjQ RRLVLVZ R\\G\\Q RfGjGjQ RTEWH ReUjZ RcCcQfW RZXUZ RbU]YZ[ R`F\\G RcLbU',
	    'Po]IkI R]NjN R]SjS R]YlY RRLWLWY R^G^[ ReHeY RUDXH RdDeG R_C\\K R[WVY',
	    'Po_[g[ RRLWLWY R_T_[ RgXg[ RUDXG RaIcK RZUVY R\\NiNeR R^IYK RbD\\J RbRcV RiUkZ RbDfHlK R\\U[Z',
	    'Po`JiJ R_NjN RaWhW Rh[l[ RRLWLWY RaRaX RdGdN RaRhRhW R]ElEl[ RUDXH RZXUZ R]E]R[Y',
	    'Po]TcT RfTlT R]ZlZ RRLWLWY R^O^[ RfOlOl[ RUDXH R[WVX R_H[M R`GkGhL RaC^J RdN]O',
	    'Po]GlG R^QjQ R`[d[ RRLWLWY R^J^Q RdQd[ R^JjJjQ RUDXH RjUkY RhTkW RcCeG R_U[XVY',
	    'Po[FlF R^NjN R[UlU R_[d[ RRLWLWY R^I^M RdSd[ R^IjIjM RUDXG RcCdF RZWVY R]QjQdS',
	    'Po[GkG R[TlT RRLWLWY RcQc[ RUDXH R^LbO RfLjP RbCcF R_I\\O RgIdO R[WVY',
	    'PoUDXH RiFgI RjQgU RbIiM RcReWgYl[ R^F\\J R^Q\\V RbJ_M[O RRLWLXYVZ RbV_YZ[ RcCbK RcNaW',
	    'Po\\QjQ R\\UjU RRLVLVY RZGZJ R\\L\\W RcIc[ R\\LjLjW RZGlGlJ RbCcE RVEWH RZWVY',
	    'Po^PhP R^VhV R[[l[ RRLWLWZ R^K^[ R^KhKh[ R[GkGkK RTEWH RbCdG R[XVZ R[G[K',
	    'Po[FlF R^KhK R^OhO R[SlS RRLWLWY R^C^O RcOc[ RhChO RcRjY RTDXH RZVVZ RcR]YZZ',
	    'PoZGkG R]KhK R]OhO RYSlS R\\[k[ RRLVLVZ R\\S\\[ R]C]S RhChS RgUhY RTDWH RaT`Y RZXUZ',
	    'Po[HlH RcLhL R^OkO R[SlS RRLWLWY R^D^O RcDcL RcOc[ RiDiL RUDXG RcSjY RZWUY RcS]YZZ',
	    'Po[FlF R[KlK R^PjP Rh[l[ RRLWLWY R]K^P R`C`I RcRcZ RcIdK RhChI RiRi[ RTDXH R[WVY RlXl[ R]R]Y',
	    'Po[GlG R\\RjR RRLWLWY R\\J\\R RcCc[ R\\JjJjR RTEXH RdSlZ RbS[Z RZWVZ R`L`P RgLfP',
	    'Po\\GcG ReNlN R]UjU R]ZjZ RRLWLWY R\\C\\N R]Q][ ReCeN R]QjQj[ RlKlN RTDXH RbNbQ RkFdI R[WVY RcL[N',
	    'PocEkE R_LgL RaRjR R^VlV RaZkZ RRLWLWY RaRaZ RUDXG RYWVZ R\\IkHkK RcCcGbJbOjNjO R\\H\\T[[',
	    'Po]HjH R]LjL R]XiX RRLWLWY R]S]X R]D]L R]DjDjL RUDXG R^O[S RdR`V RcThU RZWUY Rk[g[ R^PlPlUk[',
	    'Po\\HlH R\\LlL R^SkS R^WkW Rg[k[ RRLWLWY R\\D\\L R^O^[ RdDdL R^OkOk[ R\\DlDlL RTDXH R\\WVY',
	    'Po\\KaK ReKkK R\\NjN RZQlQ Ra[h[ RRLWLWZ R\\E\\J R\\EaEaJ ReEeJ ReEkEkJ RTDWH R[WWY R_Q^UiUh[',
	    'Po]JgJ R[QaQ R[UaU R_[a[ Rg[j[ RRLVLVZ R[M[[ R[MaMa[ ReNeW RjMj[ RTDWG RYXUY RbDeGlJ RbC^HYJ',
	    'Po[KkK RZNlN R_RiR RRLVLWX R[EcEbI RTDWG RZVVW R]VY[ R]F]I R_RcXk[ R_Q[Y RiFgJ RiSdX][ R`K^V RiCbD',
	    'Po_FfF R\\NjN RdSlS RRLWLWY R\\H\\M ReNeS R[JjJjM RUDXG R`DZJ RdGaO_R[S R[WVY RaTgV R]Wh[',
	    'Po]ZiZ RRLWLWY R]S][ R]SjSj[ RTDWH ReLjQ RYE]G RaGkGiK RbC_J R]LYN ReHcM`P\\Q RZWUY',
	    'Po[GkG RZMlM R]UiU R]ZiZ RRLWLWZ R]Q][ R]QiQi[ RUDXG R[XVZ R_H_L RbCdG RhHfM',
	    'Po\\FlF R\\LlL RRLVLVY RTDWG R`HaK RdCdE RhHfL RgN`R RYXUY RiR`V RjVeY][ R\\L\\S[Y',
	    'Po[FkF RgYjY RRLVLWY R[L[O R]Q]X RcNc[ RcCdF R]QjQjX R[LlLlO RUDXG R_G`K RhGfL RZWUY',
	    'Po_MkM Ra[l[ RRLVLVY ReCeX RSEWH RZF\\H RiRjU RdMhQ RYM\\M\\XY[ RcR_U R`EbJ R\\Xb[ RjEhJ ReLaT RXWUY',
	    'Po]KlK R_UlU RRLVLVZ R_O_[ RdOdZ RhOhZ R]FlFlK RTDXH RYWVY RdCeF R_OlOl[j[ R]F]Q[Z',
	    'PocHiH RbMlM R^SjS R^WjW Rf[j[ RRLWLWY R^O^[ RdDdL R^OjOj[ R[ElEkG RUDXG R_F\\M R[VVX',
	    'Po[FlF R]MjM R]QjQ RZUlU RRLWLWY R]J]Q R`C`H RgCgH R]JjJjQ RTDXH RcTgYl[ RZWVX RbV_YZ[ RcQaX',
	    'Po\\ThT Re[l[ RRLVLVY RYIYN R\\N\\S RbCbI ReTe[ R\\NhNhS RYIlIlN RlXl[ RTDWG R\\E]H RiEhH RZVUX R`T_X]YY[',
	    'Po\\HjH R\\LjL RRLWLWZ R\\D\\L RcDcL R\\DjDjL RUDXH RfNlP RaP^ScWj[ R_NZP RZXVY R_SiRdW\\[',
	    'PodJlJ R[JaJ R[NaN R^[a[ Rf[j[ RRLVLVY R^C^G R[FaFa[ RjCj[ RTDWH RYWUZ R[FZRaRZY ReMfS',
	    'Po]EcE R[FaF R]MkM R[RlR R\\ZjZ RRLVLWY R\\U\\Z RcMcZ RjUj[ RTDXH R]G^J RZVVZ R^M[O RbGdJ RjFhK RjDaE',
	    'Po\\FlF R\\PlP Rd[h[ RRLWLWZ R\\K\\N RdMdP RdCdF R\\KlKlN RTEXH R`W\\[ R`G`J RhGfK R[WVZ RaP_X RaUiUh[',
	    'PoZJkJ RY[l[ RRLWLWZ R\\R\\[ RaRa[ ReRe[ R\\RjRj[ RTDWG RgMjP R_L[Q RYWVZ RgDfH R^D`H',
	    'PoZHkH RZOlO R[SiS RRLVLVY R`H`[ ReGe[ R[KjKjS RSDWH RdSkZ R]D_F R`SYZ RgCeH RZWUY',
	    'Po`PhP RZ[l[ RRLVLVZ R\\S\\[ R`H`P RaSa[ ReSe[ R\\SjSj[ RTEWH RjIlM RXXU[ RhMhP R`DdF R\\I[M RhFaM[P',
	    'PoZFlF R_JgJ R\\PjP R\\SkS R]WjW RZ[l[ RRLWLWZ R\\L\\P R_C_J RcJc[ RgCgJ R\\LjLjP RTDWH RZXVY',
	    'Po]GiG R]JiJ R[QkQ RRLWLWY R]D]J R]DiDiJ RTDXH R[WVX R`M`Q RfMfQ R[MkMkQ R]S`WeYl[ R[M[Q R\\TjTgWcYZ[',
	    'Po[FlF R^OiO R_WgW Rg[k[ RRLWLWX R\\K\\[ R_S_Y RcKcR R_SgSgW R\\KkKk[ RUEXH RZWVX R`G`K RcCcF RgFfK',
	    'PoZFlF R\\KbK R\\NbN R_QbQ RgRjR Re[i[ RRLWLWZ R\\I\\Q R\\IbIbQ RfIfO RjHjQ RTDWH R_D`F R[WVY RhCeF RbTaX_Y[[ R\\TkTj[',
	    'PoRLWLWY R[DaDaL RdDjDjM RUDXG R\\G^G RcNkR ReGhH RaH[K RjIdK RcNZR RdR]T R[WWX RfT^X RiWdY][',
	    'PoZDlD R\\LkL R\\QjQ R\\TjT R[WlW RRLWLWZ R\\G\\K R\\N\\T RaDaK RcTc[ RfDfK R\\NjNjT R\\GkGkK RTDXH RZXUZ',
	    'Po[DaD RdDkD RcJlJ R\\VjV R\\ZjZ RRLWLWY R\\R\\[ RdFcJ R\\RjRj[ RhLhPlOlP RTDWH RZWVX R]LZP R[FZJ`J`N^O RdOcO R^E^M RhEgL',
	    'Po\\IkI R]LkL R]PkP R]TlT RRLWLWY R]G]U RaWa[ ReHeT RUDXG RjVk[ RYWVZ ReWfZ RdDdF R\\W[[ R^C\\I]L',
	    'Po^JiJ R^ShS Rh[l[ RRLVLVY R[I[[ R^M^S RdHdY R^MhMhS R`ElEl[ RTDWH RdRgUjX R[D^G RdR^X RaOaR RfOeR RYWUY',
	    'PoZGkG RYNlN R]UiU R]YiY RRLWLWZ R]Q][ R`G`N RfFfN R]QiQi[ RTDWH R[I]K R^C`E RjIhL RfCfF R[WVY',
	    'PoaNdN R_YgY Rh[k[ RRLVLVY R[O[[ R_U_Z RdHdN R_UgUgY R[OkOk[ RTDXG RYWUX R`R]T RZIlIhL ReQhS R\\EjDcG RcIZM R`FhH',
	    'PoYGdG ReKlK R\\TaT RRMVMVY RZJZ[ R_D^K R\\P`P_Z RZJcJc[ RTEWH RYXUY R]M]O R`L_R RhDhRk[ RjEkI RhJjY RhKfY',
	    'Po_IkI R^KlK R`QjQ R`WjW R`[l[ RRLVLVY R_F_I R`N`W ReCeK R`NjNjQ R`TjTjX R_FjFjI RTDWH RYM\\M\\X_Z RXWUZ R\\XY[ RZE[I',
	    'Po\\IlI R^OlO R`QjQ R`TjT R`ZkZ RRLWLWY R`V`[ R`VkVk[ RUDXG ReLfO R`DZJ R_EfEeH RbJ_L RgJkL RZXUY R]H]S[Z',
	    'PoZKlK RZO^O RaNeN RaQeQ RZU^U RRLVLVZ R^K^Y RaKaX RTDWG R]F`H RcFeI RiEkI RZWUY RiUc[ RkOgX RkWk[ R^D[J RcDaJ R[R^RZR RaUeU RhChQiZ ReXZY',
	    'PoWZgZ RWQW[ RWQgQg[ RcDfFjJ RZDUJ R_HgPlR R_HWPRS',
	    'PoRN]N R_ZiZ R_R_[ R_RjRj[ ReKlR RgEkH RbL\\R RXFWSTZ RaD^I RcIaN R\\DRF RXMVW',
	    'Po_IlI RUY\\Y RUQU[ RUQ\\Q\\Y RYJ\\M]P RZD^H RVFSI RWMRQ RhSl[ RXJVN RaN^[ RbJ_Y ReLcZ RbF_W RkXaZ',
	    'PobEkE R`NlN RUY]Y Rd[h[ RUQU[ RUQ]Q]Z RbEbN RiEi[ RYI_O R[D_H RfQ_Y RVESI RWLRQ RgNeS RXIVN',
	    'Po^KfK R^QfQ RUY\\Y Ri[l[ RUQU[ RUQ\\Q\\Y R^EiEi[ RlWl[ RXJ]P RZD]G RVFRI RVMRQ RXHVN RbEbRaX',
	    'Po_JkJ R_RlR RUY\\Y RUQU[ RUQ\\Q\\Y RbCbR RhChR RjXk[ RXJ^O RgVkY RYD]H RbV][ RWDRI RWLRQ RXIUO',
	    'PoaJhJ RUY\\Y R`ZjZ RUQU[ RUQ\\Q\\[ R`S`[ R`SjSj[ RYJ\\M]P RZD]G ReCkJ RWMRR ReC^J R_NiNgS RVDTI RXJVN',
	    'Po`RkR RUY\\Y R^[l[ RUQU[ RUQ\\Q\\Y R_G_J ReCeG ReReZ R_GlGkJ RYJ]N RZD]H RVDTI RWMRR RXJVO RaN_O RgGgNkMkN RcGbM',
	    'PoRQ]Q RRU^U RaZjZ RaRa[ RaRjRj[ R[O^Q RTMXN RXW\\X RgLlQ RiEkI RcM_Q RWJTL R[KUQ RXFXI R\\E^I RfIhN RTERJ RbD`I RdIbO RXWUZR[ RXRWY R\\CTD',
	    'PocQkQ R_RgR R]UlU RUY[Y RUQU[ RUQ[Q[Y RXJ\\N RZE[H RhNkQ RjFiI RVLRQ R_G`I R^EdFeI RcUgYl[ RVDTI RWIUN RhLcN RcJ^NeN^Q RcWaY][ RdScX RjCaE',
	    'PoSJ]J RSM]M RRQ_Q RSZ]Z RaZjZ RSSS[ RXCXF RXHXR RSS]S][ RRFRG^F^H RaRa[ RaRjRj[ RgKlP RiEkH RcL^Q RfHhM RbC`H RdHbN',
	    'PoTYZY RTQT[ RTQZQZY ReDkDkL RWJ\\N RYD[H R^G`H RfGhH RUMRQ ReOlR RUDSI RWITO RcI]K RkIeK R]DcDdLbO\\R ReQ_T RgT_W RiWdY][',
	    'Po]NbN R]RbR RTYYY RTQT[ RTQYQYY R]I]X R[IcIb[ RVKYMZP RgWd[ RXDZI RgRjZ RiQfZ RUDSJ RVIRQ R\\EbE`I RfJgV RfJkJhV RfCdO RdV[W',
	    'Po]ElE R^HjH R]NkN R^SjS R^UjU RTYZY RTQT[ RTQZQZY R]J]N RdCdH R^PjPjW R]JkJjN RWJ[N RYD[H RUMRQ R^P^WhXl[ RUDSI RWITO RbY\\[ RbJbN RfJfN',
	    'PoeFlF R\\FcF R\\LcL ReMkM RePkP R]RcR ReSkS R]VcV ReWkW RTYZY Re[l[ R`[b[ RTQT[ RTQZQZ[ R]N][ R^G^K R`C`F RaHaL R]NcNc[ RfMf[ ReCeIkIkM RXJZQ RYDZI RUDSJ RVJTP',
	    'PoRElE RVRhR RR[l[ RVJVR RVJhJhR RYT[Y ReTcY',
	    'PoRF^F R`FkF RTQ]Q R_[l[ RTJTQ RTJ]J]Q ReFe[ RUSUX R[SYZ R^YR[',
	    'PoUIiI RRMlM RVVhV RR[l[ RUDUI RVPVU R_C_I RVPhPhU RiDiI RZW[Z RdWcZ',
	    'PoRE]E R^HlH RSO\\O RSISO RSI\\I\\O ReCeN RURUV R`ObTgYl[ R_NjNhTcX][ RZQZV R]WRY',
	    'PoUIiI RUMiM RSOkO RVWhW RR[l[ RUEUM RVRVW R[C[M RbCbM RVRhRhW RUEiEiM RdXcZ RZYZ[',
	    'PocFjF R[LdL RXUfU RS[l[ RXPXU RXPfPfU RSJXL RjGeK RYDRJ RZW[Z RaDcIgMlO RdWcZ RWF]F[K RVG\\IWNRP',
	    'PoRE\\E RSP[P RhUkU Rf[l[ RSJSP RSJ[J[P R]G]J RfLf[ RfLkLkU R]GlGlJ RlXk[ R^PbT RaV\\[ RcQ_Y RdCeG RYRYW R_J]R R_MdMaW R\\WRY RTRUW',
	    'PoRE\\E R_KlK R_PlP RSP[P R]UlU RSJSP RSJ[J[P R_F_P RgQg[ R_FlFlP RdO_T RcCcF ReFeMbR RYRYX R\\WRY RURUW',
	    'PoSE^E RSK^K RSN^N RSQkQ RWWhW RR[l[ RSESN RWTWW RXEYH RYKYO RSH^H^K RWThThW R[Y[[ RdXc[ RbFcJgMlO R`EjEiIeM`O',
	    'PoRE[E R]HlH R]OlO RSOZO R]SjS RSISO RSIZI[O RbDb[ RfEf[ R]KjKjS RfSlY RbS[Z RUQUV RYQXV ReD]E RkDeE R\\WRX',
	    'PoVE]E RaEhE RWH\\H RbHhH RVK]K RaKhK RTMjM RSPlP RVWhW RR[l[ RTDTM RVSVW RYDYM R_C_M ReDeM RVShShW RjDjM RZX[Z ReXcZ',
	    'PoSI^I RSL^L RaMkM RRO_O RTU]U RSFSL RTQTU RWCWL RZCZL RTQ]Q]U RSF^F^L RfHf[ RaHkHkL RdU_Z RUWUY RbCcF RfPcW RiChF RkXhX RaM`RlQkX R^YXZRZ R[V[YTZ',
	    'PoUGWG RTJWJ RbMkM RSM_M RRO_O RSU]U RhYkY RWZ^Z RS[[[ RSDSL RUEVEVK RYCYL RSR]R^U R_D_L RfHf[ RaHkHkL RcCdG RiChH RdT`Y RVWVY RfPcV R[W[Z RSRTV RaMaRlRkY R\\D\\JZI',
	    'PoZH]H R`HlH RTJWJ RZJ]J RSM_M RRO_O RTV^V R_[l[ RSDSM RTFVEVM RYCYM RZF\\E\\M R_D_M RaSa[ RdSd[ RgSg[ RaSjSj[ ReMgP RhMlP RVWVY RfHiO RaP`P RTRTV RTR^R^V ReDcN RWHTH RWFTF R[W[ZR[ R\\ZU[',
	    'PoaFkF R[H^H RTJWJ R[J^J RaJlJ RSM_M RRO`PkQ RTV^V R_[l[ RSDSM RUFWEVM RYCYM R[F]E\\M RTR^R^V R_D_M RaSa[ RdSd[ RgSg[ RaSkSk[ RhMkP RVWVY RfCfJbP RTRTV RUHWHUH RWFTF RYZR[ R[W[ZU[',
	    'PoSEkE RZ[`[ RbSbY ReRkX R_NbU RaIfT R]NSS RjIhLcN RZJ`P]TRY R_E\\ISM R`FcP',
	    'PoSEkE RZ[`[ RbSbY RXN^U ReRkX R_NbU RaIfT R_E\\ISM R]NSS RjIhLcN RZJ`P\\TRY R`FcP',
	    'Po_FlF RfOfV RSF]F\\ZkYkZ RkIgMkT ReFbI^K RcM^O ReObS^T RfHiR ReWbW ReLfT R[FbF RVFUQTY',
	    'PoRE]E R_SkS Re[l[ RZOZY R_K_U RfCfZ RkKkU RlXk[ RYO^T RXERK RXLRQ RYQRX RVIYP RkE^G R]JXMZV RX[U[',
	    'PoTFjF RRKlK R[[`[ R_C_F RbTbY RYGZJ ReHcJ R\\M`QbV R`KcRgVlX RjNcQ R_K[NSP R^PYRST RaSYWRY',
	    'Po\\EkE RTKZK RTRZR RTDZDZ[V[ RTDTSR[ RdE_J\\L R`JeQeXd[a[ RcOlW RkOgR RbM_P[Q ReR_W[X',
	    'PoRE]E RhLkL RZOZY RaDhDhL RkJlL RXN^T RXERK RWLRQ RYQSW RVIYP RaPbTgYl[ R_PjOhTdX][ RX[U[ R]JXMYV RaE`L',
	    'Po_ElE RRE]E R^OlO RZPZY RiEi[ RYESJ RXLSQ RYQTV R\\JYM\\Q RX[U[ RbEbQ`X RXKYU',
	    'PoRE^E R`OkO Ra[k[ RZOZY RaEaZ ReEeO R`EkEkP RYN^S RlWk[ RYERK RXLSQ RYQSW RVHYP RX[U[ R]JYMZV',
	    'PoRE]E RaUgU Rf[j[ RZOZY RaMaV RaMgMgT RYESK RXLSQ RYQTV R\\JYM\\Q RbD^M RX[U[ RXKZV R`HlHlTj[',
	    'PoRE]E R`LkL Ra[l[ RZOZY R`GkGkL RlXl[ RZM\\Q RgSaY RbQkPgT RYESJ RXLTP RYQTV ReCfG RX[U[ R]JYLZV R`G`Q^X',
	    'PoRE]E R`LiL R`SiS R\\ZlZ RZOZY R`D`Z R`DiDiZ RYESJ RXLTP R]JZM\\Q RYQTVRV RX[U[ RXKZV',
	    'PoUNiN RVHVN RTIiIiN RXFbF_I_MbSfXlZ R`UTZ RjPbS R[CUHRJ RaNSR R\\SSV R[P_TaY_[[[',
	    'PoXGfG RSKkK RaTaZ RbIhO ReDiH RYDTH RXOfNlP R^R`UjY R_C\\IWNRP R`OWS ReRaT R`UVZ R_SZUUV R`[[[',
	    'Po^EdE RfElE RRE\\E ReOlO RZPZY RhEh[ RXERK RWLRQ RYPRW R\\IXL\\P]S RdN]P RYZU[ RVHYNZV RaEaR`X',
	    'PoRE]E R_JjJ R_OjO RT[X[ RZOZY R_E_Z R_EjEjO RfVk[ RYN^T RWLRQ RYQSW RjQfU RXERK RVHYP RdPhX RfX]Z R]JXMZV',
	    'PoRE]E R^HlH R_OfO RZOZY RgVl[ RcK_O RiKaT RYESJ RXLTP R]JZM\\Q ReDeF RdHbL RkQdX^[ RYQTVRW RX[U[ RXLZV',
	    'Po^EiE R\\KgK RYKcK R_MjM RWPlP R][a[ R_C_M RbVbZ RbPcTgXlY RTHjHjJ RjRdU RaP\\RWS R_TWV R^RaTbW[YVZ RTHTSSZ',
	    'PoRE]E R^GlG R_PkP R_UkU Rh[k[ RZOZY R_K_[ ReCe[ R_KkKk[ RhDkE RYESJ RXLTP RYQTV R]JZM\\Q RX[U[ RXKZV',
	    'PoRE]E R]MlM RZPZY R`P`X ReNe[ RXL]Q R`P\\T RWLRQ RYPSW RXERK RVHYP R`EiI RiCfF^J RcJ_RkQjXhX R\\IXLZU RX[U[',
	    'PoRE^E R`ElE RZOZY RfPfY RYN_T RdL_Q RlJhN RfQ_X RYERK RXLSQ RYQSX ReE_L RVIYP RhOkW RgKjV RX[U[ Re[a[ R]JYMZV ReLfV RfEhR',
	    'PoRFlF RVLhL RVQhQ RSNSQ RVHVK R_C_F RVHhHhK RSNkNlQ RaQbUeXk[ RiScU R_QTT R^TTW R\\R`V[YR[ R`V_[\\[',
	    'Po^NkN RS[V[ RWMW[ R^H^N R\\JkIkN RUIXK RfTkY R`FgFeI RRE\\EWJ RaE[J RdMhV RRM[MZR ReN\\R RcR[U RkQfS RaPdTbW[Z Rd[`[ RcQeUeY RdJeQ',
	    'PoRE]E R_GhG R]LlL R`UjU R`ZjZ RT[X[ RZOZY RaPa[ RcCcL R_QjQj[ RYN^T RfNiO RYERK RXLRQ RYQSW RVIYP RjDhI`P\\R R]JYMZV',
	    'PoRE\\E R^KcK RfLkL R^PcP R^UcU RYNYY R^D^[ R^DcDdK RfDkDkK RgVl[ RXN[Q\\T RjUdZ RXERK RWLRQ RYQRW ReQiY ReOkOhX RX[U[ R[KXLYU',
	    'PoRE]E R_FkF R^KlK RZPZY R_O_W RbHbM ReCeH RhHhM R_OkOkW RYESJ RXLTP RYQTV R]JZM\\Q RgXlZ RdX^[ ReRdX RX[U[ RXKZU',
	    'PoRE[E R]EdE RcQjQ R^RgR R]UlU RYPYY RgNkR RjFhI RWFSJ RWLSQ RXQRW R[JXM[Q R^G_I RdFdI RdTeWgYl[ RgLcN RcJ^NdN^Q RdTbXaY\\[ RX[U[ RWKYU RjDaE',
	    'PoRF_F RSH]H RiLlL RUN\\N RSP^P RYCYH RSKSL^K^N RcEcK RcEiEiL RdTjZ RcQfW RXRZV^W RZPSS RYTSV R]RYWRY RaPjPgUcY_[ RYSZY RY[V[',
	    'PoRE]E R_IjI R_NjN R\\ZlZ RZOZY R^R^Z R_E_M RbRbZ RfRfZ R_EjEjM R^RjRjZ RYESJ RXLSP RYQTV R\\JYM\\Q RX[U[ RXKZV',
	    'Po_ElE RRE]E RbMjM RbQjQ RYOYY RbIbQ RbIjIjQ RYESK RXLSQ RYQSV R\\JYM\\Q RiTlY RbT`Y RgEeI RX[U[ RfQf[c[ RXKYU R_E_S][',
	    'PoTH]H RaHjH RcWfW RRYlY RRFR[ R_D_Y RgNgV RlFl[ RYM^R ReMjR RfHaM ReLbP RjKgObT RYHTM RXLUP R]KZOTU RZNZVVW',
	    'PoRE\\E R^FlF RaOiO RbWhW Rh[k[ RZOZY R^K^[ RbSbY ReKeR RbShShW R^KkKk[ RZL\\P RYETJ RXLTP RVURV RYQTV RbGbK RdCdF RhFgK RX[U[ R\\JYLZV',
	    'PoRE\\E RgSkS RYNYY R_K_[ RXN\\Q RdGfJ RiHlK RWLRP RYPRV RXERK RcSdY R_D]I R`I]P ReDcL RiDgK RX[U[ RZIXLYT RgMgZl[ RcPaZ',
	    'PoRE\\E R^FkF R]IlI R_PjP R_TjT R_WjW RYOYZ R_M_W RaGaL RdCdH RhGhL R_MjMjW RXO\\R RXERK RWLRQ RYQRW RgXl[ RbX\\[ RX[U[ R[KXMYU',
	    'PoRE]E R^FlF R_IkI R`OjO R_UjU R]ZlZ RZOZY ReCeI R_QjQjU R^L^MlLlN RYESJ RXLTP RVURV RYQTV R]JZM\\Q RbWbY RhWfZ R_Q_U RX[U[ RXKZV',
	    'PoRFZF R_[e[ RWHZL R_GbJ RgXd[ RdOTT RfR`VRZ RaLfQgTgVgZ RlHaLSO RhCZF',
	    'Po`PhP Rd[j[ R]Q]Y RTHUK RXGYI RcC^M R\\IRN R[OSR RYK\\RYURX R\\DRF R[N\\W RaIlIlRj[ R[[W[',
	    'Po_JlJ Rd[h[ RV[[[ R\\Q\\Y RiCi[ RXGYI RTHUK RfO_V RhIeP R\\IRN RZOSR RYK\\RYURX R\\DRF R[N\\W',
	    'Po_ElE R^NlN RV[Z[ R\\Q\\Y ReEe[ RTHUK RXGXI R\\HZJRN RYPRR RXK[RWVRX R[CRF RZN[W',
	    'Po^LcL Rf[l[ RU[Y[ R[Q[Y R^D^Z RfCfZ RlWl[ RTHUK RWGXI RYNWPRR RkJeM RXKZRVVRX RZJRM RcY\\Z RYDRF RYN[V',
	    'Pog[l[ R\\Q\\Y R_J_[ R_JlJl[ RTHUL ReCeNiU ReMaU RXORR RXGYJ[RWURX R[ISM ReJbT RZDRF RZ[V[ RZN[V ReGdR',
	    'Po]FlF R]ZlZ RU[Y[ R[Q[Y ReIeW RTHUK RfJlP ReH]P RXGXI RfFdK RXPRR RXK[RWVRX RZJRM RZDRF RZN[V',
	    'PoaUgU Rf[j[ R\\P\\Y RaMaV RaMgMgT RTHVL RbC^M RXG[JSM RZORR R\\QXURX R[DRF R[[W[ R[N\\V R`HlHlTj[',
	    'Po`MkM R_ZlZ R[Q[Y R_Q_Y R`F`M ReCeY RkFkM RlQl[ RTHUL RXGYJ R\\IRM RYNWPRR R[RWVRX RZ[V[ RZDRF RZN[V',
	    'Po[Q[Y RTHUL RXGYJ RhSk[ RcDeH RaR][ RYNWPRR RZRVVRX R[HYJRM RbM_Y RY[U[ RZDRF R^H^L RgLiX R^HkHkL RZN[V',
	    'PogNkN R`ZlZ RV[Z[ R\\Q\\Y R`Q`[ R`QlQl[ R_ElElM RTHUK RdJ_N RXGYI ReFbM R[IRM RYOSR R[RXURX R[DRF RZM[Q\\W',
	    'Po_QlQ R_YlY R\\Q\\Y R_H_[ R_HlHl[ RXGYI RTHUK R\\IRM RZOSR RYK\\RXURX ReCdI R[[V[ R[DRF R[N\\W',
	    'Po]JlJ R^NjN R^RkR R]VlV R[Q[Y RdCd[ R^FjFjN RTHUL RXFYJ RYNWPRR RZRVVRX R\\IRM RY[U[ RZDRF RYM[U',
	    'PoaJlJ RU[Y[ R[Q[Y R_H_[ RfCf[ RSHTK RWGXI RhOlW RaC\\N ReN`X RYNWPRR RXKZRVVRX RZJRM RfHjV RfHbU RYDRF RZN[W',
	    'Po^EkE R_PjP R_UjU R]ZlZ RU[Y[ R[Q[Y R_J_U R_JjJjU RTHUK RWGXI RZJRM RXPRR RXK[RWVRX RZDRF RZN[V',
	    'Po_JjJ R_OjO RU[Y[ R[Q[Y R_E_Z R_EjEjO RfUk[ RTHUK RXGXI RcOgX RZJRM RXPRR RXK[RWVRX RkReU RZDRF RfY\\[ RZN[V',
	    'Po]OlO Rf[i[ R[Q[Y R`GjGj[ RTHUL RdRfV RdIeM ReCcG RXORR RXGYJ[RWURX R[ISM RZ[V[ RZDRF RZM[U R`G`R^Y',
	    'Po_ZjZ RV[Z[ R\\Q\\Y R_Q_[ R_RjRj[ RTHUK RbF]K RXGXI RaGbKgOlQ RbC`I R[JRM RYPSR RaGiGhJcO]R R[RWVRX RZDRF RZN[V',
	    'Po^ElE R_RkR R_ZkZ RV[Z[ R\\Q\\Y R_K_[ R_KkKk[ RTHUK RXGYI R[JRM RYPSR RXK[RXVRX ReEdK R[DRF R[N\\W',
	    'Po_HkH R_MkM R^PkP R]UlU RU[Y[ R[Q[Y R_D_M RePe[ R_DkDkM RTHUK RWGXI RZJRM RXPRR RXK[RWVRX RZDRF RZN[V',
	    'Po_KjK R_QjQ RV[Z[ Rg[l[ R\\Q\\Y R_G_Q RdCcF RgQg[ R_GjGjQ RlXl[ RTHUK RaW][ RXGXI R[JRM RXPSR R[RWVRX RZDRF RZN[V RbQaX',
	    'Po^JkJ R^PkP R^TkT R][l[ RU[Y[ R[Q[Y R^E^P ReEeZ R^EkEkP RTHUK RXGXI RZJRM RXPRR RXK[RWVRX RZDRF RZN[W',
	    'Po^KcK RfKkK R^QkQ Rf[l[ RU[Y[ R[Q[Y R^E^Q RgQg[ ReFkFkQ RlXl[ RTHUK RaW][ RXGXI RZJRM RYNWPRR R[RWVRX RcD^F RbQ`Y RZDRF RZN[W',
	    'Po^KkK R^PkP R]UlU R[Q[Y R^F^P RgQg[ R^FkFkP RTHUL RdO_T RcCdFeMbR RXORR R[QWVRX RXGZJRM RY[V[ RZDRF RZN[V',
	    'PoZQZY R]N][ RSHTK R^EdDjJ R_J[K RdD]K RVVRW RZRTW RWGWI ReTh[ RhTl[ R_QaR R^UaV R`KhJlK RgNeU RkNhT RXNVQRR RYDRG RZHXKRM RY[U[ R]NbNb[`[ RYNZU',
	    'Po^KcK ReLjL R^PcP R^UcU RZQZY R^D^[ R^DcDcK ReDjDkK RgWl[ RiUc[ RVVRX RZRTW RSHUL ReQiY RdPkPgX RXNVQRR RXFZJRM RY[U[ RZDRF RYNZV',
	    'Po^IkI R]MlM Ri[l[ R[Q[Y R^D^I R^Q^[ RcQc[ ReCeI RgQg[ RkDkI R^QlQl[ RTHUK RXGXI RdMbS RZJRM RXPRR RXK[RWVRX RZ[V[ RZDRF RZN[V',
	    'Po]GlG R_SkS R_YkY R[Q[Y R_M_[ RbCbK ReMeY RhChK R_MkMk[ RTHUK RXGXI RXPRR RXK[RWVRX R[JRM RZ[V[ RZDRF RZN[V',
	    'Po^OkO R_TdT Rf[l[ RU[Y[ R[Q[Y R^F^O R_P_Z RfPf[ R^FkFkO RlXl[ RTHUK RdCcF RWGXI RaIgK RYNWPRR RgHdK`M RXKZRVVRX RkReU RZJRM RdY\\Z RYDRF RYN[V',
	    'PocQjQ R^RgR R]UlU R[Q[Y RTHUL RgNkR RjFhI RXGYJ R^F^I R]EcFdI RdTeWgYk[ RYNWPRR RgLcN RbJ^NdN]Q RZRVVRX RcTbX`Y\\[ R[HYJRM RY[U[ RZDRF RYLZQ[V RjCaE',
	    'Po]JkJ R\\[l[ R[Q[Y R^R^[ RbRb[ RfRf[ R^RjRj[ RTHUL RhMlQ RiDgG RbL]Q RWGYJ R_DaH RYNWPRR RZRVVRX R[HXKRM RY[U[ RYDRF RYM[U',
	    'Po]HlH R_KjK R^QkQ R]TlT R[P[Y R^N^Q R_E_K ReCeQ R_EjEjK R^NkNkQ RTHUL RcQaV RYNWPRR RZQVVRX RXGYJRM R`Wk[ RY[V[ RhUeY][ RZDRF RZN[V',
	    'Po]FlF R_NjN R_QjQ R]UlU RU[Y[ R[Q[Y R_J_Q RbCbH RhChH R_JjJjQ RTHUK RWGXI RdTfWhZl[ RXPRR R[RWVRX RdVaY][ RZJRM ReRcX RZDRF RZN[U',
	    'Po]ElE RbMiM R`VdV RgVkV R]ZlZ RU[Y[ R[Q[Y R]E][ R`O`V RbHbM R`OdOdV RgOgV RbHiHiM RgOkOkV RTHUK RWGXI RYNWPRR RXKZRVVRX RZJRM RYDRF RZN[W',
	    'Po^IcI RfIkI R_PkP R_SkS R]WlW R[Q[Y R^E^I R_L_S R^EcEcI ReLe[ RfEfI R_LkLkS RfEkElI RTHUL RYNWPRR RXGXJ[RVVRX RZJRM RY[V[ RZDRF RZN[V',
	    'Po\\FlF R^McM RfMkM R^RkR R_TkT R_WkW R_ZlZ R[Q[Y R^I^M R_P_[ RaCaG R^IcIcM RePeZ RfIfM RgCgG RfIkIkM RTHUK R_P\\S RWGXI RYNWPRR RXKZRVVRX RZDRF RZJRM RY[U[ RZN[W',
	    'Po]HcH ReHkH R^OkO R^QkQ R^SlS R]VjV RZQZY R_L_S ReLeS ReDkDkJ RSHTK RWPRR RXNUQ RVVRW R_L\\O RZRTW RWGWI R]D]JaJ R]DcDcJ_J RiJeK RYDRG RYJRM R`WeYl[ R]FaF]F R_FcF_F RiFeFhFkFgF RZHWKYPZU RhJkJ RiWdY\\[ ReDeJdQ RY[U[',
	    'PoVJiJ RVOiO RVTiT RVEVT RVEiEiT RcWl[ R[WR[',
	    'Po_FlF RUNiN RURiR RUWiW RUJUV R_C_J RUJiJiV RdXl[ R[XR[',
	    'PoVPhP RVShS RVWhW RVLVW RVLhLhW RRI]IhHkK R[CYGWI RcFiH RcXl[ R[XR[',
	    'PoVNiN RVRiR RVWiW RVJVW RVJiJiW R[CTJ RYFcF_I RdXl[ R[XR[',
	    'PoSJ\\J R^JlJ RSO\\O RST\\T Rc[h[ RSDST RSD\\D\\T RhCh[ RZW]Y RUVS[ RgJdQ_V',
	    'PoUEiE RRIlI RVPhP RVShS RVWhW RVLVW RVLhLiW R_E_I RcYl[ R[YR[',
	    'PoZGkG RVGgG RVPhP RVShS RVWhW RVLVW RVLhLhW R\\D`H RkIkK RRG`GcJiK RcXl[ R[XR[ RbDfD',
	    'PoSJ[J RSO[O RST[T R`ZkZ RSDST RSD[D[T R`F`Z ReCeU RlVkZ RZV[Z RTVR[ RkI]M RkIjSgT',
	    'Po^HkH RSJZJ RSOZO RSTZT Rh[l[ RSEST RSEZEZT R`LhLi[ RYVZZ RTVRZ RcCdF RlWl[ R`L`T_Y',
	    'Po^EjE RSJZJ R]MlM RSOZO RSTZT Rf[l[ RSEST RSEZEZT RgMg[ RYVZY R`W\\[ RTVRZ RlWl[ RaN`W',
	    'Po`KeK RVQhQ RVThT RVWhW RVMVW RVMhMhW RdElH RWGfGeK RYERH R]GZKSM R[YR[ RcYl[',
	    'Po_JkJ RVPiP RVTiT RVWiW RVMVW RYEYK R`D`J RVMiMiW RkHkJ R[CXFRH RcYl[ R[YS[ RjE_F',
	    'Po_EkE RSJ[J R_LjL RSO[O RST[T RSDST RSD[D[T RZV[Y RaNdUhYl[ RUVR[ RjMgUbY_[ RaLbR R_E^R]Z',
	    'PoVQhQ RVThT RVWhW RVNVW RVNhNhW RWJfJeN R_CiH R_CTI RZGcFlH R[YR[ RcYl[',
	    'PoRGlG RVPhP RVThT RVWhW RVMVW R_D_J RWDhDhJ RVM_MhMhW RcYl[ R[YR[ RWDVJ RVKhK',
	    'PoTEjE RUHiH RRKlK RVQhQ RVThT RVWhW RVNVW R_C_K RVNhNiW RcYl[ R[YR[',
	    'Po_EeE RUIlI RYPjP RYSjS RYWjW RYLYW RUEaEaL RYLjLjW ReYl[ R]YV[ RUEUQTY RiDbD',
	    'PoUE`E RRIlI RTPaP RTSaS RTWaW RTLTV RTLaLaV RfDjF R]XbZ RWXR[ Rk[i[ RdCdLfX RlVk[',
	    'PoSJ[J R]MlM RSO[O RST[T R`[e[ RSDST RSD[D\\T R^G^J ReMe[ R^GlGlJ RZV[Z RUVR[ ReCeG',
	    'PoRFlF R^IgI RWKkK RVQiQ RVTiT RVWiW RVNVW RWDWK R^C^I RgCgI RVNiNiW RcYl[ R[YR[',
	    'PoSJZJ RSOZO RSTZT RSEST RSEZEZT RdCdN RYVZY RTVRZ R`NcTgYl[ R^HlHjL R^NjNgTcY^[ R^H^R\\Z',
	    'PoYF_F RUK]K RVPiP RVTiT RVWiW RVMVW RYCYJ RUETKXLiMiW RbCbKlJkK RcYl[ R[YS[ RjEaG',
	    'PoRGlG RTJ`J RSLaL RTQ`Q RTT`T RTW`W RTOTW RTO`O`W RgZj[ R]Yb[ RfDjE RWYR[ RlWk[ RdCdMfX',
	    'PoVIhI RRKlK RVQiQ RVTiT RVWiW RVNVW RVEVI R_C_K RVEhEhI RVNiNiW RcYl[ R[YR[',
	    'PoRKlK RWRgR RWUgU RVOhOgW RVH_HgGiI RdMlP RbEgG R]IXNRQ R]C[FVH R[YS[ RXNXWeXlZ',
	    'PoSJ[J RSO[O RST[T Rc[l[ RSDST RSD[D[T ReFeL RZV[Y RTVR[ R_V][ R^LjLfSaYi[ RkD^F',
	    'PoTIjI RVPhP RVShS RVWhW RTETI RVLVW R[E[I RbEcI RVLhLiW RTEjEjI RcYl[ R[YR[',
	    'Po]GhG RVPiP RVTiT RVWiW RVMVW RYEYK RVMiMiW RdDiD R[CXFRH RcYl[ R[YS[ R`EgKkKlI',
	    'PoSJ[J R_JkJ RSO[O R_RkR RST[T R]ZlZ RSDST RSD[D[T RdCdZ RZV[Y RUVR[ R`E^N',
	    'PoSJ[J R_NjN RSO[O RST[T Rg[l[ RSDST RSD[D[T R_E_N RgNg[ R_EjEjN RlXl[ RZV[Y R`W\\[ RTVR[ RbN`X',
	    'PoUIjI RWQiQ RWTiT RWWiW RWNWW R[C[K RcCcM RWNiNiW RSEjEjH RcYl[ RYLRN R[YR[ RTHSKkKkMdN',
	    'PodIlI RSJ[J RSO[O RST[T R_ZjZ RSDST RSD[D\\T R_P_[ RdCdP R_PjPj[ RZV[Z RUVR[',
	    'PoSJ[J R^MkM RSO[O RST[T R_ZjZ RSDST RSD[D\\T R_Q_[ R_QjQj[ RZV[Z RiIkN RUVR[ RdC_M',
	    'PoSJZJ R^NkN RSOZO RSTZT RSEST RSEZEZT R^G^[ RYWZZ RTWR[ RdWe[ RbZ]Z RlWk[i[ RiD^F RdEdLfX',
	    'PofKjK RVPhP RVThT RVWhW RTEUK RVMVW RVMiMiW RdEaK R[YR[ RcYl[ R_EkEjK RZF]IRK R\\DTE',
	    'PobJkJ RZK^K RVPiP RVTiT RVWiW RVMVW RbEbJ RVMiMiW RbEkEkJ RXCUK R[YS[ RcYl[ RTE^E^K',
	    'PoUEjE RRIlI RVPhP RVThT RVWhW RVMVW RYGYK R_C_H ReGeK RVMiMiW R[YR[ RcYl[',
	    'PoSJ[J RSO[O RST[T R_ZjZ RSDST RSD[D[T R_R_[ R_RjRj[ RZV[Z RbE]K RaGbKgOlQ RUVR[ RaGiGgJbO\\R',
	    'PoZHlH R\\KjK RWQiQ RWTiT RWWiW RWNWW RXEXL RWNiNiW RYCWFRH RcYl[ R[YS[ RhC_E RcEcK',
	    'Po]GlG RSJ[J RSO[O R`PjP RST[T R`UjU Rg[j[ RSDST RSD[D[T R`L`[ R`LjLj[ RZV[Z RTVR[ RbE^O',
	    'Po]GlG RSJ[J RSO[O RST[T RSDST RSD[D[T RfVl[ RYV[Y R_McQ RcI_M RUVR[ RdFbJ RiKaR]T RkPdW\\[ RdDdH',
	    'Po]IlI RSJ[J RSO[O R\\QlQ RST[T RSDST RSD[D[T RhIh[ RZV[Y R_CaH RTVR[ RiCgH RaIaS`X',
	    'PoVPhP RVThT RVWhW RVMVW RVMhMiW RdGfJkL R^FkFhH R`C[H RTDZF RcYl[ RYJRK RcF`J[K R[YR[',
	    'PoRDlD RTKjK RVQhQ RVThT RVWhW RTGTK RVMVW R[D[K RcDcK RVMhMiW RTGjGjK RcYl[ R[YR[',
	    'PoSJZJ RSOZO RSTZT R[ZlZ RSEST RSEZEZT R^H^Z RcGcZ RfHfZ R^HjHjZ RYVZY RTVR[ RcCbK',
	    'Po]IlI RSJ[J RSO[O R]PcP RST[T RSDST RSD[D[T R`K`X RZV[Z RiDkF RjMeZ RTVR[ RfCeMgYk[mW',
	    'Po\\HlH RSJ[J RSO[O RST[T RSDST RSD[D[T RdCdG RYV[Y RgJkN R`J\\O R`ObTgYl[ RUVR[ RhNeTaY[[',
	    'PoRElE RVRiR RVUiU RVPiPiX RcG[J RaKlN R]EVHaIUL RhHaKVN R\\YR[ RVPVXfXlZ RbKSO',
	    'Po^HlH RSJ[J RSO[O R`PkP RST[T R_ZlZ RSDST RSD[D[T ReDeH RfJfZ RZV[Y RTVR[ R^H^S]Z',
	    'PobHiH R`IfI RSJ[J RSO[O RdQjQ R]RdR RST[T RSDST RSD[D[T ReWi[ RZV[Z RlWk[ ReDjF RUVR[ RiSeX][ RfQ`Q R]IcIeW RcDcM^M RhLdL`M',
	    'PoTJ\\J RaJjJ RTO\\O RaOjO RTT\\T RaTjT RTDTT RTD\\D\\T RaDaT RaDjDjT R[W\\Z RhVl[ RcW_[ RUVSZ',
	    'PoSJZJ RSOZO RSTZT RSEST RSEZEZT RYVZZ RfKiLkO RfEjI RaP\\U RbD]I RTVR[ RaK]O R`QaTeXl[ R`RiRdW\\[ RfH]I RhH`I',
	    'Po^EkE R`IjI RSJZJ R^OlO RSOZO RSTZT RSDST RSDZDZT RaNaZ RhWl[ RYVZY RkQhS RfRiY RTVR[ ReNhX RgY^Z R^E^R\\Z',
	    'PoTJZJ R_LhL RTOZO R\\PkP RTTZT RTDTT RTDZDZT RdPd[ RdClK RcE\\L RYVZY RhTkY RUVR[ R_T\\Y R`[d[`[',
	    'PoWHgH RWQgQ RWTgT RWWgW RSFSI RWNWW R_C_F R_H_M RTOgOgW RSFkFkI ReJkL RZJTM R[XR[ RbYl[ RcLRO',
	    'PoVMiM RZRiR RZUiU RZWiW RRFRI RZOZW RZOiOiW RRFlFlI R^CaF R`I_M RdZl[ RVIiIiM R^YV[ RVIVQTY',
	    'Po\\HkH RSJZJ RSOZO RSTZT R`[c[ RSDST RSDZDZT RcCc[ RYVZY RfSlX RgDjE RjMgP R^L_P RcLhU RTVR[ RcS_U\\V RcHfS',
	    'PoTJZJ R_LiL RTOZO R\\PkP RTTZT RTDTT RTDZDZT RdLd[ RdClK RcE\\L RYVZY RiTkY RTVR[ R_S\\Y R`[d[`[',
	    'PoSJ[J R^LkL RSO[O RST[T R^UlU RSEST RSE[E[T R^O^V R^F^K ReCe[ R^FkFkK R^OlOlV RXV[Y RTVRZ',
	    'Po^GlG RSJZJ RSOZO R`PkP RSTZT R^ZlZ RSEST RSEZEZT ReJeZ RYVZZ RiSjW RdCeF RTVS[ R^G^S\\Z',
	    'PoaIjI RSJ[J RaMjM RSO[O RST[T RbWiW Rg[l[ RSDST RSD[D[T RbRbX ReGeP RbRiRiW R_ElEl[ RZV[Y RUVR[ R_E_R]Z',
	    'PoSFkF RVQhQ RVThT RVWhW RWMWW R_C_L RTNiNhW RWHYJ RfIjJ RfGdJ RXGTK R_GcKkO R_G[KRO RcYl[ R[YS[',
	    'PoTE^E R`EkE RRH^H R`HkH RVQhQ RVThT RVWhW RVMVW RVMhMhW ReDeIhKlL RXI]K ReHcK`L RYCXIVLRM RcYl[ R[YS[',
	    'Po_IkI RSJ[J R_MkM RSO[O RST[T Rf[j[ RSDST RSD[D[T R_D_M R_DkDkM RZV[Z R_Q]S RUVR[ RaM_QlQj[ RhQfWc[ RcQbT^X',
	    'PoSJZJ RSOZO R\\QlQ RSTZT RSDST RSDZDZT R]E]H RcNc[ R]EkEkH RfEfLkKkL ReRlY RcObS[Y RYVZX RTVR[ RbQ`U R_L\\M RbE`K',
	    'PoWMgM RVRiR RVUiU RVXiX RSGSJ RVOVX RWIWM R_C_G RWIgIgM RVOiOiX RSGkGkJ RVDXF RhCeF RcYl[ R[YR[',
	    'PoSJYJ R]MjM RSOYO RSTYT R[TlT RSEST RSEYEYT R]G]T RbCbT RfCfT R]GjGjT RgVk[ RXVYY RaV\\[ RTVRZ',
	    'Po]GkG RSJ[J R\\NlN RSO[O RST[T R_ZjZ RSDST RSD[D[T R_R_[ R_RjRj[ RZV[Z RhHgM RUVR[ R`HaM RdCdG',
	    'PoTElE RVKlK RZShS RZUhU R`C`F RYPhPhX RYHhHhM RXNfNlQ R`G^L\\NVQ RZOZXfYk[ R]YV[ RTETSS[',
	    'PoSD^D RSI]I RSL^L RVQhQ RVThT RVWhW RSDSL RVNVW RYDYG RXIYL RSF]F]I RVNhNhW RaFeJlL R`EjEfI`L RcYl[ R[YR[',
	    'PoRElE RTGjG RTMjM RVRiR RVUiU RVXiX RTJTM RVOVX R[J[M R_C_G RcJcM RVOiOiX RTJjJjM R[YR[ RcZl[',
	    'PocGjG R`GgG R]GeG RSJ[J RSO[O RdRlR RbRjR R]SeS RST[T RSDST RSD[D[T RZV[Z RkKlN RlXk[ RaEcJfMjN RdOhP RiTdWfZj[ ReDhE RUVR[ RhJ_N RgW][ RaOcW',
	    'Po]GlG RSJZJ RSOZO RSTZT R\\TlT RSEST RSEZEZT RdQd[ RYVZY R`LcO RgLkP RTVRZ RdCeG RhIeP RaI]P',
	    'Po]FdF RSJZJ R\\JlJ RSOZO RaQeQ RSTZT RSDST RSDZDZT R]N]Z RaLaY RYVZY RiEkG RTVR[ RlVk[ RfX[Z RgCgNgPiZ',
	    'PoSJZJ RSOZO RSTZT RSEST RSEZEZT RYVZY RiFfI RjQfU RTVRZ RcJiL RcSdVgYl[ R_E]I R_Q]V RaL\\N RbV_Z[[ RcOaX RcCbL',
	    'PoWHhH RSKkK RVRhR RVUhU RRERH R_C_F RVOhOhW RRElElH ReLlO RYMRO R_K_O RVOVWeXlZ R[YR[',
	    'PoSJZJ R_LiL RSOZO R\\QlQ RSTZT R`[d[ RSEST RSEZEZT R]G]J RdQd[ R]GkGkJ RYVZZ RhTkW R_V\\Y RUWRZ RcCeG',
	    'PoTH^H RaHlH RVPhP RVShS RVWhW RVLVW RZHZK RgHgK RVLhLhW RcYl[ R[YR[ RUDSK RaD`J RiCbD R\\CTD',
	    'PoSEkE RVRhR RVThT RVWhW RSMSP RVOVW R_C_M RVOhOhW RSMkMkP RfGbK RYGUJ RWI\\J ReIkJ RcYl[ R[YR[',
	    'Po_DjD R_HiH RSJ[J R_LiL RSO[O R\\PlP RST[T RSDST RSD[D[T R_D_Z ReUjZ RZV[Y RiTeU RjRhU RcPfW RTVR[ ReY\\[',
	    'Po^GhG RSJ[J R\\LlL RSO[O RST[T RaUjU RaZjZ RSDST RSD[D[T RaPa[ RcCcL R_QjQj[ RZV[Z RfNiO RUVR[ RjGaP\\R',
	    'PoRGlG RVJiJ RRLlL RVSiS RVUiU R_C_L RVEiEiJ RVPiPiX RaMbO RhMkP RUMRP R[M[P RVPVXfXl[ R\\YS[',
	    'PoSJZJ R_KfK R_MfM RSOZO RfPiP RSTZT R\\VlV R`[d[ RSDST RSDZDZT R_H_M RdTd[ R_HfHfM R_E\\H RYVZY RTVR[ R^RjRdT R_FkFjO',
	    'Po]FkF RSJZJ R]JjJ R\\MlM RSOZO RSTZT R_VhV R_ZhZ RSDST R_P_[ R^RiQi[ RgNlS R`O[T RYV[Y ReJhQ RTVR[ RbI^R RcD`O RSDZD[TXT',
	    'Po^IkI RSJZJ RSOZO R^PkP R^TkT RSTZT R\\WlW RSDST RSDZDZT R]D]G R^L^T RdGd[ R^LkLkT R]DlDlG RYV[Z RTVR[',
	    'Po\\HiH RYJlJ RVRiR RVUiU RVWiW RWFWM RVOVW R\\D\\H RcHcM RVOiOiW R\\DiDiH RWERJ RfKkM R_KYM RcYl[ R[YS[',
	    'Po[GdG RSJYJ RZLlL RSOYO RSTYT R\\WcW RSEST RSEYEYT R\\O\\Y R`C`L R\\OcOcV RXVYY RhEkH RhUb[ RjOfW RTVRZ RfCfNhY RlVk[i[',
	    'PoRG^G RgKjK R`PkP RSQ]Q R`SkS R`WkW RSKSQ RXCX[ RSK]K]Q R`M`V R`MkMkV RiXl[ RXR\\V RWTRX RdG`K RXOVU RdEbJ RcX^[ R`EkEjJ',
	    'PoaHjH RSJ[J RaLjL RSO[O R_SkS RST[T R_WkW R_ZkZ RSDST RSD[D[T R_D_L R_O_[ R_OkOk[ R_DlDlL RZV[Z RUVR[',
	    'PoSJYJ R^OiO RSOYO R^RiR RSTYT R^ZjZ RSEST RSEYEYT R^L^[ R\\LiLiR R]UjUj[ RbIdJ RcEjL RcE\\L RXVXZ RSVR[',
	    'PoSEkE RUJ\\J RbJiJ RVRhR RVThT RVWhW RSMSP RUGUJ RVOVW RUG\\G[J R_C_M RbGbJ RVOhOhW RbGiGiJ RSMkMkP RdXl[ R[YR[',
	    'Po_HiH R[KlK R_QiQ R_TiT RV[Z[ RTDZDZ[ R^OjNiW RVIXK RVOXR RfJlP RgEkH R`E\\H RaK\\P RfHhM R_M_WhXk[ RcE_N R`Y\\[ RTDTSS[',
	    'Po]JkJ RSJZJ RSOZO RSTZT R\\[l[ RSEST RSEZEZT R^R^[ RbRb[ RfRf[ R^RjRj[ RYWZZ RhMlQ RiDgG RaL]Q R_DaH RTWR[',
	    'Po\\HkH RSJZJ RSOZO R\\OlO R]SjS RSTZT RSDST RSDZDZT RbDb[ RfEf[ R]KjKjS RYVZY RfTlZ R_V[Z RTVR[ RaQ^X RjDdD]E',
	    'Po\\FlF RSJZJ R^LkL RSOZO R^PkP R\\TlT RSTZT Rd[g[ RSDST RSDZDZT R^I^R RdCdQ RgRg[ R^IjIjQ R`VbX RYV[Y RgDjD RTVR[',
	    'Po\\FkF R]IkI RSJZJ R\\LlL RSOZO R^SjS RSTZT R[WlW Re[j[ RSDST RSDZDZT R^O^[ RaCaL RdLdV RgCgL R^OjOj[ RYV[Z RTVR[',
	    'PoUHhH RVJhJ RRMkM RWRfR RWUfU RSESH RZGZN RVPgPfX RSEkEkH RdGdMfOlR RYORR R[YR[ RXOXXdXk[',
	    'Po]EkE RRE\\E R^HkH R\\JlJ R^PjP R^TjT R^WjW RRX\\X RRERZ RTKTS RWSWX R^M^W RdCdJ R^MjMjW RgYl[ RaY[[ RWEWKZKZSWS',
	    'PoSJYJ RSOYO RSTYT RSDST RSDYDYT R\\DbDbL ReDkDkM RgFeI RhIeL R_F\\I R`J\\N RXVZY ReOlR RTVR[ RcM`P[S RdR^U RfT^X RiWdY][',
	    'PoSD^D RVG]G RiGlG RaIjI RSM^M RVRhR RVUhU RSDSM RVOhOhX RcDiDiG RUI[I^K RYJUK RdKlN RYFXJ RcDbG`G RhL`N RVOVXfXl[ R[YR[',
	    'PoTG]G RTI]I RTR]R RTT]T RTW]W RTOTW RTD]D]K RTO]O]W RfUl[ RTDTK\\L^N R[X][ RfT`[ RcNhX RiNeW RVMRN RVYR[ RbIeU RbIkIfU RcCaM',
	    'Po^GiG RSJYJ RSOYO RSTYT RSEST RSEYEYT RYVZZ ReLkKkM RfDjH RfHeL RbC^G R^H\\L RcMlQ RTVR[ RbN\\Q RcQ]S RfS^W RiV_[ RaJbL[L',
	    'PoSJZJ R^JjJ RSOZO R]QkQ RSTZT Ra[h[ RSDST RSDZDZT R]M]R R^F^J RaVa[ RdCdS RhXh[ R^FjFjJ R]MkMkR RYVZY RiVlZ RbTeU RTVR[ R^V\\[',
	    'PoTD]D RRG_G RSJ_J RSL^L RVRhR RVThT RVWhW RVOVW RYCYG RVOhOhW R`FiFjM RlKlN RdJ`M R\\G[J RVGVJ RaHfK R[YR[ RYJYM RdDcK RbYk[',
	    'PoTD^D RbFlF RRG_G RSI_I RVRhR RVThT RVWhW RVOVW RYCYI RVOhOhW RWJTM RcE`H RbGfKlN RiGfJ`M RWK]LZM R[YR[ RcYl[',
	    'Po\\GlG RSJYJ RSOYO RdRlR R[RcR RSTYT RSEST RSEYEYT R^SbW RaNbP RiNkP RhDiF RWVXY RgRkZ RcGfKkM RgSdZ RTVRZ RbH`K\\M RcCbJ R^P]Y RgOfW',
	    'Po_DeD RSF]F R^GlG RbIfI R_MgM RVRhR RVThT RVWhW RVOVW RXCXF R_I_M RbGbL RVOhOhW RiDkE RTHZK RlLlN R[GVLRM RcYl[ R[YS[ RgChL',
	    'PoSJ[J R^KlK RSO[O R^OlO RST[T R_VkV R_ZkZ RSDST RSD[D[T R^G^O R_S_[ ReGeO R_SkSk[ R^GlGlO RZV[Z R`CaF RjChF RTVR[',
	    'Po]DlD RSJZJ RbKfK RSOZO R^QjQ R^TjT RSTZT R\\WlW RSDST RSDZDZT R]H]L R^N^T RbDbK RdTd[ RfDfK R^NjNjT R]HkHkL RYV[Y RTVR[',
	    'PoTF]F RaFkF RRH^H R`HlH RgLlL RVQiQ RVTiT RVWiW RVMVW RYCYHZL RfCfHgL RVMiMiW RcIaL RVJSM RbDaG R^KYK RcYl[ R[YS[',
	    'PoUElE R_JjJ R_LjL R_OlO RYSjS RYUjU RYXjX RYQYX R[H[O R`H`O ReGeO RYQjQjX R\\FVK RaF\\K R^YV[ RdYl[ RUEURSZ',
	    'Po\\IlI RSJYJ R\\NkN RSOYO R]QkQ RSTYT R\\UlU RSEST RSEYEZT RaCaI RfCfI RXVYY R]D^G RjDiG RTVRZ R`J`N RdNdUgYl[ RhJgM RdU`Y[[',
	    'Po]IlI RSJZJ RgLkL RbNiN RSOZO R^PlP RaSjS RSTZT RaUjU R`ZkZ RSDST RSDZDZT R`W`[ RgHgL R`WkWk[ RZVZY R`FgEeH RUVR[ RcHaL R`E]I^S\\Z',
	    'Po]FlF RSJZJ R^MjM RSOZO R^PjP RSTZT R_XiX Rh[k[ RSDST RSDZDZT R]S][ R^I^P RaCaH RdIdW RgCgH R^JjIjP R]SkSk[ RYV[Z RgUiY RTVR[',
	    'PoTEkE RUHjH RVMiM R\\RaR R\\UaU RTVYV R\\WaW Ri[l[ RU[X[ RTETH RVJUM RTPYPY[ R\\P\\W R\\PaPaW RVJhJhM RdPiPj[ RlXl[ ReTgV R\\YZ[ RTPTVSZ RdPdVcZ RXSTS',
	    'Po\\HlH RSJZJ R^KiK R\\NlN RSOZO RSTZT R[[l[ RSDST RSDZDZT R]T]Z RbTbZ RbOaR RdCdN RfTfZ R^EjEjK R]TjTjZ RYVZY ReOfR RiOlR R^P\\R RTVR[',
	    'Po`EkE RSF]F R_IlI RRJ^J R`OjO RTP\\P R`SjS RTS\\S R`VjV RRW^W RTMTS RXSX[ RTM\\M\\S R`L`V R`LjLjV RgXkZ RXCXF R[GZJ RUGUI RbY][ ReEeI',
	    'PoSD^D R`HkH RSJ]J RSM^M RcMhM RVRhR RVThT RVWhW RSDSM RVOVW RXDXG RXJXM RSG]G]J R`E`H RcJcM ReCeH RVOhOhW R`EkEjH RdJfJ RiJlM R`J_M RZYR[ RcYl[',
	    'Po^GlG RSJXJ R[M^M RSOXO RSTXT RaWeW RSDST RSDXDXT R[F[M R^G^V RaJaX RaSdScW RaNeNeS RiDkE RgXd[ RXVXZ RjPfY RTVR[ RaJeJcN RkKhW RlWk[ R[Q[X RhDhQiZ RZQ^Q\\[',
	    'PoWGhG RWIhI RTR]R RaRjR RTU]U RaUjU RTW]W RaWjW RTPTW RTP]P]W RaPaW RWDhDhK RaPjPjW R[X\\Z RhYkZ RVYR[ RdY_[ RWDWKeLkN R[MSN',
	    'Po_ElE R`HkH R_NkN R`SjS R`UjU R[V]V RYFY[ R[K[P RYF]F]V R_J_N RcJcN ReCeH RgJgN R`PjPjW R_JkJkN R`P`WiXl[ RTFUTRT RcY^[ RXDSF RWHWRVW',
	    'Po\\ElE R^HkH RSJZJ RfMkM R]NkN RSOZO R^SjS RSTZT R^UjU RSDST RSDZDZT R]J]N RdCdH RfJeM R^QjQjW R]JkJkN RYV[Y R^Q^WhXl[ RTVR[ RbL_M RaY\\[',
	    'PoUDlD R^IdJlK RVSiS RVViV RZGZO R^F^L RdDbF RVQiQiX R[EVK R]N\\O RVQVXfYl[ RfHjH RjMjOhO R[YR[ RhMkM RcMfL RUDUJTO R^HbH^GjFiIfHcH RiMfL',
	    'PoSJYJ R_MiM RSOYO R]RjR RSTYT R]TjT R]WjW RSDST RSDYDYT R\\G\\J R]O]W R_J_M RdCdG R_JiJiM R]OjOjW R\\GkGkJ R^D`F RiDfG RfYl[ RUVR[ RaY[[ RXVYZ',
	    'PoXD]D ReDkD RTF_F RaGlG RVIZI]J RcIgIkJ RhNlN RWRhR RWUhU RWXhX RWPWW RYCYJ RWL[L[O RfCfJ RWPhPhW RdLcN RWLVO RdLhLhO R]NZN R[YR[ RcYkZ RTGSN RaG`N',
	    'PoeFlF R[FcF RSJXJ RZLcL ReNkN RSOXO ReQkQ R\\RbR ReTjT RSTXT R\\VbV ReWkW Re[l[ R_[b[ RSEST RSEXEYT R\\N\\[ R\\H\\K R_C_F R\\NbNb[ ReNe[ ReCeIkIkN RXVXZ RSVR[ RaG`L',
	    'PobFjF RSF^F RRK^K RbKiK R`NkN RTP]P RaSjS RTS]S RaUjU RRW^W RaXjX RTMTS RXCXF RXSX[ RTM]M]S RaPaX ReKeN RaPjPjX RUGUJ RhYl[ R[G[J RdE_G RcY^[ RbFeHlJ RiGeI_J',
	    'Po[FlF RSJXJ R^JlJ R`MfM RSOXO R[O^O RSTXT R`XfX RSDST RSDXDXT R[J[O R^J^X R`C`I R`M`Y RcMcQ R`TdTcX R`QfQfT RlXl[ RhWd[ RXVXY RiHkI RkPgY RTVS[ R[SZZ RfCgIhRiZ RZS^S][',
	    'PoVEVT RVEiEiT RbVl[ R_S[XR[ R`H`O_T',
	    'Po_FlF RVJVV R_C_J RVJhJhV RaWlZ R_N_T\\XXYR[',
	    'PoWKWV RWKiKiV RVJRK R[CTK RZFdF`K RbWk[ R`N^V[XXZS[',
	    'PoZJjJ RXJgJ RUJeJ RWNWV RWNiNiV ReFjK R\\CWI RaWl[ R_Q^V[YR[',
	    'PoTEjE RRJlJ RVNVV R_E_J RVNgNhV R`WkZ R_Q^V\\XR[',
	    'Po_IlI Rd[h[ RTETT RTE\\E\\T RhCh[ RYV\\Z RWWR[ RdR^W RhHcS RXHXRWW',
	    'PoTFjF RUIiI RRLlL RVOVV R_C_L RVOhOhV RaWlZ R\\XR[ R_Q^W',
	    'PoSESM RVPVW RYDYN RVPgPgW R^FcKkN R]EiEeJ\\N R`Xj[ R^U[YR[ R^R^W',
	    'PobIlI RTETT RTE\\E\\T RdTk[ RYV]Z RVWR[ ReU`[ R`LeV RhNdW RbD`M RiJfV RiHgS RXHXQWW',
	    'Po]NlN RTETT RTE[E[T R`D`[ RgUlZ RXV\\Z RVWR[ RjEcL RdNhW ReY_[ RWHWRVW',
	    'PoaLlL RWOWW RYFYM RbCbL RWOhOhW RkIlL R[CXFRI RaWl[ R_U\\YS[ R_Q_W RkE\\H',
	    'PoWIlI RYNYV RYNiNiV RcWk[ RaU^XW[ RUFUQSY RUF\\FaE`N RaP`V RdD_E RiDaE',
	    'Po_LkL RTETT RTE[E[T RdTjZ RXV[X RVWR[ RbNfW RkNgUcY`[ RbLdU RWHWRVW R_E_R^Y RkD`E',
	    'PoWPWV RWPhPhV R^G`I RVKgKcN R_DfHlI R`XjZ R_U[YT[ R_Q^W R_DUIRI',
	    'Po_MdM RWOWW RWOgOgW RcCeFlI R^H[LUN R[DXGRI R`Xj[ RWHeHdM R^U\\XYYS[ R_Q^W',
	    'Po^MjM Rd[l[ RTETT RTE[E[T R`WcZ RXV[Y RVWR[ RiOaW RcHdK R^W\\[ RWHWRVW RjD^F',
	    'Poe[j[ RTETT RTE\\E\\T RYV]Z RVWR[ RbC_L RbK`U RfOhT_T RaHlHlSj[ RXIXRWW',
	    'Po^YlY RTETU RTE\\E\\U R^H^M R^HkHkL RYW\\Z RVWR[ RcDeG RXHXQVW',
	    'PoRHlH RVLgL RWOWW RWOhOhW RaWlZ RXEVL R_U\\XR[ R`D^L R_Q^W RXEhEgL',
	    'PoRGkG RTJaJ RRNbN RTQTW RTQ`Q`W RhCjF R\\Xb[ RZSZWWYR[ RlYl[i[ ReCeLgY',
	    'PoTETT RTE[E[T RlXk[ RYV\\Y ReEiF RVWR[ RgZj[ RkQfV]Z RcCcLeW RXHWRVW RgI^J RiH`J RkM]P',
	    'PoUFjF RRKlK RVOVW RYHYM R_C_H ReHeM RVOhOhW RaXk[ R_R^V[YR[',
	    'PoRFlF R]JfJ RVMjM RVDVM RVPVW R]D]J RfDfJ RVPhPhW RbWk[ R`WjZ R_R^V[YR[',
	    'PodIlI R_ZkZ RTETT RTE\\E\\T R_Q_[ RdCdQ R_QkQk[ RYV[Z RVWR[ RXHXRWW',
	    'PoVIhI RRLlL RWOWW R_C_L RWOgOgW RVEhEhI RVFVI RaWkZ R_U\\XYYS[ R_Q^W',
	    'Po_NjN Rg[l[ RTETU RTE[E[U R_E_N RgNg[ R_EjEkN RlXl[ RYV\\Y RVWRZ RaW][ RbO`X RXHXQVW',
	    'PodHlH RaHiH RWOWW RXFXM R`DbJ RWOhOhW ReDhE RlKkM RZCSJ R[H`HcHeLjM RaXk[ R_U\\XT[ R_Q^W',
	    'PoeMjM RTETL RVOVW RVOhOhW RdI`M ReFbL RaWlZ R[G]KSL R^WYYR[ R_EkEjM R_Q^W R\\CTD',
	    'PoUIjI RWQWX RcCcO RWQiQiX RTEjEjI RUITMlMkO R\\C\\KYNSQ RaWl[ RhOkOhO R_S^W[YR[',
	    'PocLlL RZL^L RXOXW RcEcL RXOiOiW RcElElL RXITM RbWlZ RYCWK R`U]XZYT[ R`Q_W RSF_F^L',
	    'Po`MkM R]MhM R_ZkZ RSEST RSE[E[T R_Q_[ R_QkQk[ RXV\\Z RjJlN RgHjJ RVWR[ RaI]M RdCaI RWHWRVW',
	    'Po]HlH R]PcP RTETU RTE[E[U RXV[Y RgDjF RVWRZ RgUa[ RjMfW RlWk[ R`K`Q_W ReCeJeNgY RXHWQVW',
	    'Po^FhF RRF\\F RVPVW RXCXN RTNWOgPgW RiNlMlN RbJ^N R_IeL R`XjZ R^U\\XYYS[ RhGiN R^R^W RcCaK R\\IRJ',
	    'PoRDlD RTMjM RTHTM RWPWW R[D[M RcDcM RWPhPhW RTHjHjM RaXk[ R_S^W[YR[',
	    'Po]GlG R`PjP R`UjU Rg[j[ RSEST RSE[E[T RaJa[ R_MjLk[ RXV[Y RVWR[ RbF^O RbD`M RWIWRVX',
	    'PoYG_G RbLlL RTETM RWOWW RYCYL RbCbL RWOhOhV RlJkL RaWlZ RjEaG R_R]V[XXZR[ R^KTM',
	    'PoZIlI R\\MjM RWPWW RXEXN R\\EdEcM RWPhPhW RYCWFRH RaXjZ R]XT[ R_R_W RiDbD',
	    'PoaGkG R`ZjZ RTETT RTE[E[T R`R`[ R`RjRj[ RYVZZ RVWR[ RbF]K RaGbJgNlP RbC`I RjGeN]Q RWHWRVW',
	    'Po_GlG RaPkP R_ZlZ RTETT RTE[E[T RfJfZ RYW[Y RdCfE RVWR[ RXHWRVW R_G_S][',
	    'PoWPWW RWPhPhW R^FkFiI R_D[I RcHeKlN RTEZG RcFbJ`L[N RaWlZ R`R^V[YT[ RZKRL',
	    'Po^GlG R^OgO RTETU RTE[E[U RgVl[ RYV\\Y RVVRZ RbK^O RbS^T RiLaT RdCeF RcHaM RkQdX][ RXHXQWW',
	    'Po_KkK RTETT RTE[E[T R_EkEkK RdXg[ RXW[Y RcPfS RVWR[ RgMkU R`VeY RXIWRVW R_E_N]U',
	    'Po]HkH Ra[d[ RTETU RTE[E[U RdCd[ RYV\\Z R^L`N RgTlX RgDjF RVWRZ RdQ]X RkLfP RdKiV RdHgS RXHWQVW',
	    'Po_EkE RaIjI R_NlN RTETT RTE[E[T RbNb[ RhVlZ RXV[Y RVWR[ RkQgT RePiX RfYaZ ReNgV RWHWRVX R_E^S][',
	    'PoTEjE RSJkJ RWPWW R_C_O RWPhPhW RXGZJ RfGcJ R_JfNlO RaWlZ R_JXNRO R_R^W[YR[',
	    'Po`LiL R^PkP Ra[e[ RSESU RSE[E[U RePe[ RXW\\Z RhTkX RdEkK RaT_X RdE^L RTZRZ RWHWRVW',
	    'Po^FdF R\\JlJ RbQeQ RTETT RTEZEZT R^N^Z RbLbY RYVZY RiDkG RlWk[ RTZRZ RgCgNhZ RWHWRVW RfY\\Z',
	    'Po^GhG R]MlM RaUjU RaYjY RTETT RTE[E[T RaPa[ RdCdL R_QjQj[ RYW\\Z RVWR[ RjGbO\\R RXHWRVW',
	    'PoSFlF RSMSP RVQVW R_C_M RVQhQhW RSMlMlP RgGcL RYHUL RXI\\K RfJkK R_S]X RbXl[ R[ZR[',
	    'Po_FjF R]TlT RTETT RTE[E[T RdCdJ RhXl[ ReWiX R^PbQ RYV[Y RaMcN RVWR[ RdUaY][ R^JkJjN RWHWRVW ReLeQdV',
	    'PoXNfN RSGSK RWQWW R_C_G RWQhQhW RSGkGkK RWDZG RgDdG R_T]X RaXl[ RXJXN RXJfJfN R\\YS[',
	    'Po_IkI R_MkM R`QlQ RTETT RTE[E[T R_D_M R_DkDkM RXV[Y RVWR[ RaP]S RcT^Y RhQdZ RbM_R RdQaW Rk[g[ RlRk[ RWIWRVW',
	    'PoTOTW RVEVK RTO]O]W RaOaW RVEhEhK RaOjOjW RZX][ RgXkZ R^G]K RWYRZ RdX_Z RaKlM RXRWX ReRdX R\\KSM',
	    'PoaIiI RaMjM RbViV Rg[l[ RTETT RTE[E[T RbQbX ReFeM RbQiQiV R_DlDl[ RYVZY RVWRZ RWHWRVW R_D_S]Y',
	    'PoTEkE RWLlL RZQZX RYRgRgX RXIhIhO R`C`F RXOfOkS R`T`X R_M[QVS RbXkZ R`G^O R^YW[ RTETSS[',
	    'Po^GkG R]NlN R_ZjZ RTETT RTE[E[T R_R_[ R_RjRj[ RYW[Y RVWR[ R`HaM RdCeG RiIgN RWHWRVW',
	    'PoTETT RTE[E[T RXV[Y RiMkO RiRfU RiGfI RcJgK RcSeVgYl[ R_E]I R_Q]V RaL]N RTZR[ RbV_Z\\[ RWHWRVW RcCbL RcObW',
	    'PoRG^G RSQ]Q RSKSQ RXCX[ RSK]K]Q RaIaU R_KjKjU RXQ]V RVTRX RgXkZ RXPUV RcC`K RbFiFfJ RdWaZ^[ ReOdX',
	    'PoaHjH RaLjL R`SkS R`WkW R`ZkZ RTETT RTE\\E\\T R_D_M R`O`[ R`OkOk[ R_DlDlM RYV[Y RVWR[ RXHXRWW',
	    'PoSE_E RTG^G RRJ_J RYO]O RWQWW RYCYI RVL^L]O RWQgQgW RWKSO RcE`H RbHfLlN R_S]X RbGjFhJeL_O RaXj[ R\\YR[',
	    'Po]FlF R^MkM R^PkP R]TlT Rd[h[ RSESU RSE[E[U R^I^R ReCeQ RhRh[ R^IkIkQ R`VcY RXV[Y RgDkD RTZRZ RWHWRVW',
	    'Po^HlH R]OlO R^SjS RTETT RTE[E[T RbHb[ RgGg[ R^KjKjS RXV[Y RfTlZ R_DaF RVWR[ R`V\\Z RhDfI RbR_X RWHWRVW',
	    'PoUIiI RVLhL RROlO RSFSH RXQXX RVRgRgX RSFkFkH RdGdOfQlT RZGZOXQRS RaXkZ R_T^W[ZS[',
	    'PoRE\\E R^FkF R_IjI R]LlL RRY\\Y RRERY RXEXJ RUJURYRXY RUJZJZR R`O`W ReCeL R`OjOjW RfXl[ RdVbY][ ReRdX',
	    'PoUElE R_JjJ R_MjM R_OlO R[H[P RZQZW R`H`P RfGfO RZQiQiW R\\FVL RaF\\K RcXk[ RaT`W^YV[ RUEUQSZ',
	    'PoTF]F RaFjF RRI^I R`IlI RgMlM RWOWW RYCYIZM RfCgIgM RWOhOhW RUDTG RbC`G RVIUMRN RdIbM_M R`XjZ R^LYM R]YT[ R_R^W',
	    'Po_DdD RRF\\F R]GlG R^NfN R]NcN RVPVW R^I^N RbGbM RVPhPhW RXJ[M RTHXJ RiDkE RZHUMRN R`WlZ RbJeJbJ R_R]V[YS[ RgChM RlLlNjN',
	    'Po_OlO R`VkV R`ZkZ RTETT RTE\\E\\T R_G_O R`R`[ ReGeO R`RkRk[ R_GlGlO RYV\\Y RaIbM RVWR[ RiChG RiIhM RbCbF RXHXRVW',
	    'Po_IlI RaOlO RbQkQ RbTkT RbZkZ RTETU RTE[E[U RbVb[ RbVkVk[ RYW\\Y RfLfO RhJkL RVWR[ RdJaL RaEhFeH RXHWRVW RbC_I_S^Z',
	    'PoRElE RUHjH RUMiM RTSYS RTWYW RU[Y[ Ri[l[ RUEUH RUJUM RTPYPY[ R[P[W R[PaPaW RUJiJiM RdPiPj[ RlXl[ R_WaY ReRgW R\\ZZ[ RTPTVS[ R^R]Y RdPdUcY',
	    'PoSF^F RRJ^J RbLjL R_PlP RTP]P RTT]T RRW^W RTMTT RUGVJ RXTX[ RTM]M]T RaRaX ReLfP RaRjRjX RXCXF RcD`G RgYl[ R[GZJ RbFjEgH`K RfUdY_[ RbFdHhJlJ',
	    'PoUGiG RRMlM R^[c[ R_C_M RcMc[ RgQkU RVRRV R\\M[VV[',
	    'PoTG^G RRL_L RX[[[ R[L[[ RgVl[ RcV^[ R]P_U RfHfRiY ReRbX RTPRV ReOgW RbHkHjN RbC`N RYCXLWPUY ReLdU',
	    'PoTG^G RSL_L RYCYL RTPSU R^P_U RdC`O R[L[[X[ RWLWQUY RbIkIfU`Z RbIfUgWmZ',
	    'PoTG^G RaKkK RRL_L RYCYL RaDa[ RaDkDkK RdTk[ RiTc[ RbOfW RaOkOgW RTPSU R]P^U R[L[[X[ RWNUY',
	    'PoTG^G RRL^L R`RjR RW[[[ R[L[[ R`I`Q ReCeY R`IjIjQ R]O_S RTORU RYCXLWQVX RiTkY_Z',
	    'PoTG]G R`RkR Ra[k[ RW[Z[ R[L[[ RaJaZ RfJfR RRM_LkKkS RlWk[ R]P^U RcC_M RcGhFfL RTPRV RYCXLWQUX',
	    'PoeFlF RTG]G RRL^L RZLZ[ R`J`U ReCeJ R`JkJkU R]O^T RgXlZ ReMeVcX^Z RTORU RW[Z[W[ RYCXLWQUY',
	    'PoTG\\G RaGjG RRL]L R_LlL Rd[g[ RXCYLZ[ RgLg[ R\\O]T RiPlU RaP_U RTORU RW[Z[W[ RWLVQUY ReCdLcQbX',
	    'PoeFkF RTG]G RRL^L R`NkN R`RkR R`WkW RW[Z[ RXCXL RZLZ[ R`J`W ReCeJ R`JkJkW RiXl[ R]P^U RWLVTS[ RbY^Z RTPRV RVOUX',
	    'Po`GhG RTG]G R_LlL RRL^L RbUkU RbZkZ RW[Z[ RZLZ[ RcPc[ RdCdL RaQkQk[ RhNjO RiI`R RkEhJ R\\P]U RTPRV RXCXLWRUY',
	    'PoSG\\G R^KcK RfLkL RRL\\L R^PcP R^UcU RW[Y[ RXCXLY[ R^D^[ R^DcDdK RfDkDkK RgWl[ RjUdZ RfRiY RePkPhX R[O\\T RSOSU RVLVRUY',
	    'Po_FlF RSG\\G RRL\\L R_MlM RaQjQ RaZjZ RaTa[ ReHeT RaJjJjQ RaTjTj[ RdCgF R\\O\\T RTORU RXCYLY[V[ RVLVRUY R_F_S]Z',
	    'PoUGiG RSMkM R`SiS R_C_Z RWPWUSZ RWU\\Ye[l[',
	    'PoUGiG RRLkL R][l[ R_C_L RWO\\S RfP^X RVTTZ RVUZZc[',
	    'PoSG^G RRM^M RYR^R ReWiW R[ZlZ RUUYY RTSWX RXIYP RXCYMYYaZ R`IjIjPiV RTPS[ ReDdJcS',
	    'PoTG^G RSM_M RYR_R R[[l[ RbFbS RiCiX RUVYY RUSWX RlQ`S RZCYYaZ RUPSZ',
	    'PoTG_G RSM`M RZR`R RdCdX RdKlO RUPSZ RTSYZa[k[ RZCZZ',
	    'PoTG^G RSM_M RYR^R R[[l[ RUVYY RdQ_V RUSWX R`GcKfNhQjV RiFcR RZCYYaZ RUPSZ',
	    'Po`EkE RSG^G RRL_L R_NlN RYR^R R[ZlZ RfEfX RUUYY RTSWX RYCYYaZ RTPS[',
	    'PoSG^G RRL^L RaMjM RYR^R RaWkW RbMbW RaEjEjM RlSkW RTPS[ RYCYY RSUYYa[l[',
	    'PoSG]G RRL^L RYR^R RaVjV R[[l[ RaHaV RfDfV RkHkV RTUYY RYCYYa[ RTPS[',
	    'PoSG]G RRM^M RYR^R RdWiW R[ZlZ RUUYY RaKeM RTRWX RbC_K RhP_S RYCXYaZ RaGkGjOiW RTPS[',
	    'PoSG\\G RRL]L RYR]R R\\ZlZ RTTYY RcJfM ReFkL RfTeW RcG^K R_PiPgT RdDbH RYCYYaZ RTPS[',
	    'PoSG^G RRL^L RYR^R R[[l[ RfDfP RTUYY RiFlM RiNhReU_X RbG`N RYCYYa[ RTPS[',
	    'PoTG^G RSM^M RYR^R R\\[l[ RUUYY RcS_W RTSWX RePjUjW RbHkHiL ReNbU RbD`L ReJdS RYCYYaZ RTPS[',
	    'PoSG[G RRM\\M R]OlO RXR[R R[[l[ R^IiIiO RTSUWXY RdDcOjW RbS]X RXCXYa[ RTQS[ RcIbS',
	    'PoSH^H RaLlL RRM^M RYR]R R\\ZlZ RhLhX RTTYY RYCYYaZ RTPS[ RaFaO`V RjEaF',
	    'PoSG]G R`LlL RRM^M RXR]R R\\[l[ R`F`W RlUlX RTTYY RfU^W RgEgLhV RjD`E RYCYYaZ RTPS[',
	    'PoSG]G RRM]M RXR]R R[[l[ ReCeY RUVYZ ReN^U RTSWX RjUgU R_OkNjU R`H_N RYCYY`[ RTQSZ RkF`G',
	    'PoSG^G RRM^M RYR^R R[[l[ RUVYY RfDlJ RdE_K RUSWX RfKaM RgO`R RjQfU_X RYCYY`Z RTPSZ',
	    'PoSG]G RRM^M RXR^R RbXeX R[[l[ ReGeW RUUYY RTSWX RaHkHjK RiMkU RbC`K RaM_U RYCXYaZ RTPS[',
	    'PoSG\\G R`IkI RRM]M R^OlO RXR]R R[[l[ RUVYY RfCePjU RdS^X RTSVX RaE`K RYCYYa[ RTPSZ ReIdR',
	    'PoSG^G RaKjK RRL^L RaQjQ RYR^R R^WlW R[[l[ RaEaW RaEjEjW RTUYY RYCYYa[ RTPS[',
	    'PoSG]G RRL^L RfMjM RXR^R R`WjW R`O`W R`OjOjW RTPS[ R_EkEjL RdEcJ`M RSUYZ`[l[ RYCYZ',
	    'PoSG]G RRL]L R`MkM RYR]R R[ZlZ R`E`W RTSVVYY ReSeW RcV_V RfEfKgU RYCYYaZ RUPSZ RjDbE RlTkXiW',
	    'PoSG]G R`HkH RRL^L RYR^R R_VlV R[ZlZ ReDeH RUUYY RTSWX RiKgV RbKcT RYCYYaZ RTPS[',
	    'PoSG]G ReHlH RRL^L RXR^R R`VjV R[ZlZ R`O`X ReCeO R`OjOjX RUUYY RTSVX RYCXY`Z RTPS[',
	    'PoSH]H R`LjL RRM]M RYR]R R`WjW R\\ZlZ R`O`W R`F`L ReDeW RjFjL RjOjW RTTYY RYCYYaZ RTPS[',
	    'PoTG]G R`HlH RRM^M RYR^R R`H`U RhDkF RkLeW RdR]V RTPSZ RfCeKgVkWmS RTUYY`[l[ RYCYY',
	    'PoSG]G RRL]L R`QjQ RYR]R R_VjV R\\ZlZ R`LjLjV RTTYY RbD_J RaGhFeL RYCYYaZ RTPS[',
	    'Po_GkG RSG]G R`LkL RRL^L RYR]R R`VjV R\\ZlZ R`P`V ReDeL R`PjPjV RTTYY RYCYYaZ RTPS[',
	    'PocFjF RTG^G RSL^L RdOlO RYR^R R\\[l[ RUUYY RjGeLaR RkQfV ReC_I RTRVW RaHgJ_N RdRgU_Y RYCYYaZ RTPSZ',
	    'PoSG\\G R`HkH RRM]M R^MlM RXR]R R[[l[ ReCeX RTOTUUVYY RhQlU RcQ^V ReLhR RaD_K ReLbS RXCXYaZ RTQS[',
	    'PoSG\\G R^JlJ RRM]M RXR\\R R`VjV R[[l[ R`P`V RfEfP R`PjPjV RUUYY RTSVX RXCXYaZ RTQS[ RiD_F',
	    'PoSG\\G RRM\\M RXR\\R R_WcW RgWjW R[[l[ R]EcEcW ReEjEjW RUUYY RhGgK RaG_K RTSVX RiMfS RaM^S RXCXYaZ RTQS[',
	    'PoSG]G R^HbH RSL]L RXR]R R[[l[ RTUXY ReS`W RgHgPjV RgNcU RdHkHjM RbN]P RgLiU ReCbM RYCYYaZ RTQS[ RgJfR',
	    'PoTG\\G RRM]M RYR]R R\\[l[ RfCfWkVkW RTTYY RgNjQ R_GaK RjFhK RbN]P R_V]W RYCYYa[ RTPSZ RbCbNaT',
	    'PoSG\\G RhLkL RRM\\M R^PkP RXR]R R[[l[ ReMeX RbEhEhL RTUYY RdP_V ReRlU RXIXP RXCXMXYa[ RTQS[ RbEaL',
	    'Po]EeE RSG[G RRL\\L RXR\\R RhXkX R[ZlZ RgFgR RkDkW RUUYY R_OaR]V RTSWX RcL`T R`E^N R_JdJaR RXCXYaZ RTPS[',
	    'PoTG]G R`HkH R`LkL RSM^M R_OkO RYR^SlT R\\[l[ R`E`L ReOeY R`EkEkL RUUYY RTSVW RYCYYaZ RTPS[',
	    'PoSG\\G RRM]M R^QkQ RXR]R R]WlW R[[l[ RdCdW RUVYZ RaJcM R`HaL RhIkL RTSWX R`E^N RiEgM RYCYYa[ RTPSZ',
	    'PoSG]G R`KjK RRM]M RePkP RXR]R R[[l[ R`E`K R`EjEjK RbTeW RUVYY RTSWX RaQcV RaN_W RfKeWkX RYCYYa[ RTPSZ',
	    'PoSG\\G R^IjI RRM\\M RXR[R ReXkX R[[l[ ReLeX R^EjEjI RkUkX RUUYY RTSVX RiK`L RXCXYaZ RTQS[ R^E^O]V RjO`P RkR_T',
	    'PoSG^G RRM^M R`NjN RYR^R R`SjS R\\[l[ R`J`X ReCeJ RTTYY RhElI RaF^I R`JjJjWfX RYCYYaZ RTPS[',
	    'Po^GkG RSG\\G RRM]M R_PjP RXR\\R R\\[l[ R_J_P ReCeY R_JjJjP RUUYY RdPgSkW ReP^W RTSVX RYCYYa[ RTPS[',
	    'PoSG[G R`KiK RRM\\M R^PkP RXR\\R R]WlW R[[l[ ReKeW RTUYY ReEkK RiRhV ReD^K R_RaV RWIXP RWCXMXYa[ RTQS[',
	    'PoSG\\G RRM\\M R^OhO RXR]R R[[l[ RcIcW RTUYY RcPgT RgJfM RcMbQ]U R`JaM RaC_I RjWfX RXIXP RXCXMXYa[ R`GkGkRiX RTQS[',
	    'PoSG\\G RRL]L RYR]R RbWhW R\\ZlZ RbQbW RhThW RTTYY RcPeR RdHfJ R_LiLeO RdC_H RjQjU ReDhGlI R^Q^V RYCYYaZ RTPS[',
	    'Po_GlG RSG]G R_KlK RRM^M RXR]R R`RjR R`VjV R\\[l[ R`N`X RcCcK RhChK R`NjNjX RUUYY RYCYYaZ RTPS[',
	    'PoSG\\G RRM]M RXR\\R RgXjX R[[l[ R_J_X RbMbT ReCeJ R_JjJjX RUUYY RhEkH RaF^I RTSVX RXCXYaZ RTQS[ RbMgMgSbS',
	    'PoeFlF RSG]G RRM^M R`MjM R`QjQ RXR]R R^TlT R\\[l[ R`J`Q ReCeJ ReQeY R`JjJkQ RTTYY RYCYYaZ RTPS[',
	    'PoSG]G R`HkH R`LjL RRM^M R`QjQ RXR]R R`VkV R[[l[ RaGaX RgFgV RUVYZ RTSWX ReDfH RbC_J RYCYYa[ RTQSZ',
	    'PoSG\\G R`HiH RRL]L R^LlL RXR\\R RaXeX R[ZlZ ReLeW R`EjEjL R_ObQ RUUYY ReQiSkU RjOgR RTSWX ReQ^U RXCXYaZ RTPS[',
	    'Po\\DiD RSG\\G RSL]L R^OdO RYQ]Q R^D^U RdDdY RdT[U RTQSZ RSUYYa[k[ R^IcI ReIkIiSgX RgKiSkW RYCYY',
	    'PoSG\\G R_KkK RRM]M RXR]R RbWjW R[[l[ R_F_I RcOcV RaQjPjV R_FkFkI RUVYY RdDgF RcN_S RTSVX RcKbQ RYCYYa[ RTPSZ',
	    'Po]GlG RSG\\G RRM\\M RXR]R R_SjS R_WjW R[[l[ R_K_X R_KkKkX RTUYY RaDbF RcGbN_R RiCgHgPkP RXIXP RXCXMXYa[ RTQS[',
	    'Po^EaE RSG\\G RRL]L RXR]R R_WjW R[[l[ R_P_X R_PjPjX RTSUVXY RgKkO RcFkFjI RdCbH RbJ]J RfJcM`N RfFgL RYCXYaZ RTQS[',
	    'PoSG]G RaHjH RaKjK RRM]M R_NlN RXR]R ReRkR R\\[l[ RaDaK RaDjDjK RaSdW RUUYY RTSVX RaQ^W RfNeWkX RYCYYa[ RTPS[',
	    'PoSG\\G R`KhK RRL]L RfNjN RXQ]Q R`UhU RfXjX R[[l[ R`H`K RhRgU RhHhK RUVYY RaC_H RTSWX R`Q`U RaM_S RcL_N RdRcW_X RdGcL RYCXY`Z RaFkFjM R`PkPjX RTQSZ',
	    'PoSG\\G R^HjH R^KjK RRM\\M R]OjO RXR[R R]SkS R[[l[ R^D^K RaKaS RdDdK RgKgS R^DjDjK RTUXY RfUjX R`V\\X RXCXYa[ RTQS[',
	    'Po^FjF RSG\\G R]JkJ RRM\\M R^PjP RXR\\R R^SjS R[[l[ R^M^S RaCaJ RdJdS RgCgJ R^MjMjS RUUYY RgUjY RaU^X RTSVX RXCXYaZ RTQS[',
	    'Po^EdE R^GlG RSG\\G RRM]M RaMhM RXR\\R RbUgU RhXkX R[[l[ R^O^X RaIaM RbRbV RbRgRgU R`JiIhM R^OkOkX RUUYY RgGjJ RcF^K RTSVX RXCXYaZ RTQS[ RiCaD',
	    'Po_FjF RSG\\G R]IlI R^LcL ReLkL RRM]M ReOlO R]RlR RXR[R R[[l[ RdCdI RhJhO RgRgWlVlX RUUYY RTSVX RbR`W\\X RaJbO]O RXCXYaZ RTQS[',
	    'Po^HkH RSG[G R_KjK RRM\\M R_NjN R_RkR R[[l[ R_F_S RaTaX ReEeR ReTfX RTUYY R`D_H[J RiTkX R]T\\X RWJXQ RXR\\R RWCXMXYa[ RTQS[',
	    'PoSG[G R_HhH RRM\\M R]NbN ReNkN R]RlR RXR[R R[[l[ R]KbKbN RdPdX R_EhEhH ReKkKkN RTUYY RbRfTkV RdQ`U\\W R]K]N R_E_H ReKeN RWIXP RWCXMXYa[ RTQS[',
	    'PoSG\\G RRL]L R_OkO R`RjR RXR\\R R`TjT R`WkW R[[l[ R`N`X R^EcEcK RfMfW ReEkEkK RUUYY RhFfH R`N]Q RiHfK RaF^H RaH^K RTSVX RXCXYaZ RTQS[',
	    'Po]EkE RSG[G RRM[M RXR[R R[[l[ R^I^X RdEdX R\\JkIkX R_RbU RTUXY R_MbO RfMhO RbM_O RhLeO ReRhTeU R`F\\J RaQ`U RfGkI RgXjXgX RWIXP RWCXMXYa[ RTQS[',
	    'Po^FcF RfFkF RSG\\G RSL\\L RjLlL RXQ\\Q R[[l[ R_N_U R_NjNjU RUVYY RTSWX RiCiGjJ ReUkX R^D^G RaC`H^L RfDeF RgHfL R]HcHcLaK RcV^X ReHkHjL RdQcV RYCXY`Z RTQSZ',
	    'PoSG\\G ReGlG R]GdG RjKlK RSL\\L R_OjO RXQ]Q R_RjR R_UjU R[[l[ R_M_U R_MjMjU RUVYY RTSWX RgWlY R^D_G^L RfDfGfK RaCaFcJaJ RbW]Y RiDjK RYCXY`Z RTQSZ',
	    'PoVMiM R_SiS RVEVM R_M_Z RVEiEiM RXRTZ RWUYW_Ze[l[',
	    'PoTL[L RWQ\\Q RTETK RTPTY RWLWX RTE[E[K R\\WRY RgEgJiX RaFaK_W',
	    'PoSK\\K R_PhP RXQ\\Q Re[j[ RSDSK RTOTY RXKXX RSD\\D\\K RbC]M R]WRY R`IlIlTj[',
	    'Po_EkE RTL[L R^NlN RXQ\\Q Ra[e[ RTETK RTPTY RXLXX RTE\\E\\K ReEe[ R\\WRY',
	    'Po`IlI RTL\\L RXQ\\Q R_[l[ RTETL RTPTY RXLXY RTE\\E\\L R`MjM_X RlXk[ RbC^M R]WRZ',
	    'PoREkE RaMgM RVSiS R_VjV R][l[ RVOVS R_S_Z R[IhIgM RVOiOiS RXVS[ R\\EZJXLSN RZYcZ',
	    'PoTL\\L R]OlO RXQ\\Q RTETL RTOTY RXLXX RTE\\E\\L R^HiHiO RbV_Y[[ R`RaVdXgZl[ R\\WRY RdCcQbW',
	    'Po^IjI RSL[L R]PlP RWQ\\Q RSESK RTPTY RWLWX RSE[E[K RfVk[ RbU\\Z RdCdQhY RdPaW R\\WRY RdLcU',
	    'PoSK[K R`LhL RWQ[Q RSDSK RTOTY RWKWX RSD[D[K ReElL RbF\\L R^QjQe[ RcCaH R\\WRY',
	    'Po]EdE ReElE RSK[K RdOlO RWQ[Q RSESK RTOTY RWKWX RSE[E[K RhEh[ R]Z[[ RdN\\O R\\WRY RaE`Q_W',
	    'Po^HlH RSK[K RXQ\\Q Rd[i[ RSDSK RTOTY RXKXX RSD[D[K R_W[[ RdDeG RaR^Z R\\WRY RbHbOjNjUi[ RbK`V',
	    'PoTL[L ReNkN RXQ\\Q R[[l[ RTETL RTPTZ RXLXY RTE\\E\\L R`J`[ ReCe[ R\\XRZ',
	    'PoTK[K RWQ[Q RTETK RTPTY RWKWX RTE[E[K RdTk[ RaEaMfW RfMkMhUcY_[ R`O]Z R^EhEfM R\\WRY RaI^X',
	    'PoaKlK RTL\\L RXQ]Q R`SlS Rg[l[ RTETK RTPTY RXLXX RTE\\E\\K RaElEl[ R_W[[ RaE`S]Y R]WRY R`M_W',
	    'PoTL[L R^QkQ RWQ\\Q R_[k[ RTETL RTPTZ RWLWY RTE[E[L R_E_[ RdEdP R^EkEkR RlWk[ R\\WRY',
	    'Po]HlH RSL[L RWQ\\Q RSESK RTPTY RWLWX RSE[E[K RdCdN R_OaTfXl[ R]NjNhScW[[ R\\WRY',
	    'PoSK[K R]NkN RWQ[Q RSESK RTPTY RWKWX RSE[E[K RgWl[ RbU\\[ RdEeQiY RdQaX RdLgW RdMbU R[WRY RdJeT RjD^F',
	    'PoTL[L RcUgU R`[j[ RTETL RTPTZ RWLWY RTE[E[L RaMa[ ReFlM RkWj[ RbG\\M RdDfH RcCaH R[XRY RWQ[Q R`MhNgU',
	    'PoSL[L R_MlM RWQ\\Q RSESK RTPTY RWLWX RSE[E[K RgMg[ RbPlU R\\WRY RjD_F_S^Y',
	    'PoTL\\L R^MkM RXQ]Q R_ZjZ RTETL RTOTY RXLXX RTE\\E\\L R_Q_[ R_QjQj[ RiIkO RcC_M R]WRY',
	    'Po\\KlK RSL[L RWQ\\Q RdSiS R_ZkZ RSESK RTPTY RWLWX RSE[E[K R_D_[ RdCdU RiDiU R\\WRY',
	    'PoSL[L RWQ\\Q RSESK RTPTY RWLWX RSE[E[K ReElL ReJ`P RbF\\L RcCaH RgOcS_T RjSfX^[ R\\WRY',
	    'PoSK[K R^PkP RWQ[Q RSESK RTPTY RWKWX RSE[E[K RaVf[ RdKeN ReElL RjRdX RbG]M RcCaI R\\WRY',
	    'Po^HjH RSL[L R\\OlO RWQ\\Q RSESK RTPTY RWLWX RSE[E[K RdCd[ RgTlY RdMgT RdMaU\\XRY',
	    'Po\\JlJ RSK[K RWQ\\Q RSDSK RSPSY RWKWX RSD[D[K ReUk[ RgDjG R^WZ[ RfU`[ RaCaMfW RiMdW R`R\\Z R\\WRY RaI_U',
	    'Po_IkI RTK[K R]PlP RXQ[Q RTETK RTOTY RXKXX RTE[E[K RfVk[ RbU\\[ RdCdQhX RdPaX R_E]M RdNeU R[WRY RdLcU',
	    'Po^JlJ RTK[K RXQ\\Q R_ZjZ RTETK RTPTY RXKXX RTE\\E\\K R_Q_[ ReCeQ R_QjQk[ R\\WRY',
	    'PoTL\\L RXR\\R Ra[k[ RTETL RTPTZ RXLXY RTE\\E\\L R^H^L RaLa[ ReCeH R^HkHkL RlWk[ RjO`S R]XRZ',
	    'PoSKZK RfXlX RSDSK RTPTY RWKWX RSDZDZK RfFf[ RfFlFlZ R_S[[ RZQWQ R[XRY R`C_O^W R\\JdJcTbZ^Z',
	    'PoSLYL RWQZQ Rf[l[ RSESL RSPSZ RWLWY RSEZEZL R^J^Z RaDaY RgDg[ RlJfO RlWl[ RdMaM RZXRZ RdY[Z',
	    'PoTL[L RWQ[Q R_SfS RfUiU R_[k[ RTETL RTPTY RWLWY RTE[E[L R_K_[ R_KfKfR R_GjGjT RlXk[ RaC]K R\\XRY',
	    'PoTK[K RWQ\\Q R_TeT Rd[i[ RTETK RTPTY RWKWX RTE[E[K R_J_U R_JeJeT R^EkEj[ R\\WRY',
	    'Po^IlI RTL[L R_RkR RXR\\R R^[l[ RTETL RTPTZ RXLXY RTE\\E\\L ReIe[ RdCeG R\\XRZ',
	    'PoSK[K RXQ\\Q R`UfU Re[j[ RSDSK RTOTY RXKXX RSD[D[K R`M`V R`MfMfU RaC]M R\\WRY R`HlHlSj[',
	    'PodJlJ RSL\\L RXQ\\Q R_ZkZ RSESK RTPTY RXLXX RSE\\E\\K R_Q_[ RdCdQ R_QkQk[ R]WRY',
	    'Po^ElE RTK\\K RXQ\\Q RaZkZ RTETK RTOTY RXKXX RTE\\E\\K RaMa[ R`OkOk[ RbI]T RcE`Q R\\WRY',
	    'PoaJlJ RSLZL RWQ[Q Re[i[ RSESK RTPTY RWLWX RSE[E[K R`G`[ RiCi[ RcNeT RaC\\M R[WRY',
	    'PoTK[K R]MkM RXQ\\Q R]SlS RTETK RTPTY RXKXX RTE\\E\\K RdCd[ R^FaK RjEhK R\\WRY',
	    'PoSL\\L R_LiL RXR\\R R_SiS R\\[l[ RSESL RTPTZ RXLXY RSE\\E\\L R_D_[ R_DiDi[ R]WRZ',
	    'PoSLZL R[OlO RWQ[Q Rh[j[ RSESK RSPSY RWLWX RSEZEZK R]D][ RbDb[ RfDf[ R]DjDj[ R[XRY',
	    'PoTK[K RXQ\\Q RTETK RTPTY RXKXX RTE[E[K RdCdN R`NbTgYl[ R^NjNhTcY^[ R^HkHjL R[WRY R^H^R\\Z',
	    'Po_KkK RTK[K RWQ[Q Rc[k[ RTETK RTPTY RWKWX RTE[E[K RcMcZ R_EkEkJ RlWk[ RkObS R[XRY R_E_R]Z',
	    'Po`DlD RSK\\K RXQ]Q R`SjS R`ZlZ RSDSK RTPTY RXKXX RSD\\D\\K R`D`[ R`KjKjS R]WRY',
	    'Po_EfE RTK[K R]NlN RWQ[Q Ra[d[ RTETK RTPTY RWKWX RTE[E[K ReHe[ RiRlY R_R]Y R\\WRY R^F]N RjDaE',
	    'Po_JjJ RSL[L R_OjO RWQ\\Q RSESK RTPTY RWLWX RSE[E[K R_E_Z R_EjEjO RfVk[ RjSfU RcOgX R\\WRY ReY\\Z',
	    'Po]FlF RSKZK R^NkN RWQ[Q RSESK RSPSY RWKWX RSEZEZK R^JkJkN RbU^Z RdCdRfWiYl[ R[WRY R^N]RkRkWhV RdMcU',
	    'Po]HlH RTL[L RWQ\\Q Rc[g[ RTDTL RTPTY RWLWX RTD[D[L RdCdH RgHg[ RjMlT R^M\\U R\\WRY RbHbPaV\\[',
	    'PoSK[K RcQlQ RWQ[Q RSESK RTPTY RWKWX RSE[E[K RbTfV RcIeL ReC_I RgK^O RbGkGfMaS]T R[WRY RkRfWbY][',
	    'Po_IkI RSK[K R]OlO RWQ[Q Rg[l[ RSESK RTPTY RWKWX RSE[E[K RdCdO RgOg[ RlXl[ R`W\\[ R`D^L R\\WRY RbO`X',
	    'Po]HlH RTL\\L RXQ\\Q RTETL RTOTY RXLXX RTE\\E\\L RdCdG RgJlN R`K]O RaOcUgYl[ RhOfUaY\\[ R]WRY',
	    'PoSK[K RfNjN RWQ[Q R]RlR RSESK RSPSY RWKWX RSE[E[K RdOd[ RfTlZ RcS]Z R_M\\N RbE`L R[XRY R^EfEfIkHjN',
	    'Po`IjI RTK[K R]PkP RXQ\\Q RTETK RTPTY RXKXX RTE\\E\\K ReCe[ RhUlY RbT\\Y ReNhV ReNaU RaE^M R\\WRY',
	    'PoSL[L R_NjN RWR\\R R_TjT R][l[ RSESL RTPTZ RWLWY RSE[E[L RdNd[ RfGlM RbH]M R_EeEgI RdDbI R\\XRZ',
	    'Po]GlG RSK[K RaLhL R^PkP RWQ\\Q Rc[h[ RSDSK RTOTY RWKWX RSD[D[K RfHlM RbH\\N RdC`K R`TjTh[ RaP`V R\\WRY',
	    'PoTK[K R^LkL RWQ\\Q R^QkQ RTETK RTPTY RWKWX RTE[E[K R^G^S R^GkGkQ RiSfW^Z RlWk[i[ R\\WRY RdCdOfX',
	    'Po]KlK RSLZL RWQ[Q RfUjU Rb[k[ RSESK RSPSY RWLWX RSEZEZK RbNbZ RbNjNjU RkWk[ R`FhFeJ R[WRY RaC^J^S]Z',
	    'PoSE\\E RhMkM RVSiS R_VjV R][l[ RVOVS RWEWK R_S_Z R`DhDhL RVOiOiS RkIkM RbGeJ RXVS[ R_J\\M RZYcZ R`E_K R\\JRL',
	    'Po_HjH RTL[L R]NlN RWR\\R R^TkT R\\[l[ RTETL RTPTZ RWLWY RTE[E[L ReCeN RePe[ R\\XRZ',
	    'PoTKZK R]QjQ RWQ[Q R]WkW RTETK RTPTY RWKWX RTEZEZK RdNd[ RbC]I R`GdKlN R]S]W R`FiFeJ]N RZWRY',
	    'Po^ElE RSL[L R_MkM RWR[R R_TkT R][l[ RSESL RTPTZ RWLWY RSE[E[L ReOe[ RbI^M RiIkN RdEaK R\\XRZ',
	    'PoSK\\K RXQ\\Q R`ZjZ RSDSK RTPTY RXKXX RSD\\D\\K R`Q`[ R`RkRj[ R`H]K RaGbKhQlR RbC_J RaFiFgKaP\\R R\\WRY',
	    'Po]IlI RSK[K R]PlP RWQ[Q RSESK RTPTY RWKWX RSE[E[K RhIh[ R`W\\[ R`DaH RiCgH R\\XRY RaIaR`W',
	    'Po^GkG RSL[L R]LlL R]QlQ RWQ[Q Rc[h[ RSESK RTPTY RWLWX RSE[E[K RdCdL RhLh[ R_TcW R\\WRY',
	    'PoSL[L R`LhL RWQ[Q R_ZjZ RSESK RSPSY RWLWX RSE[E[K R_Q_[ R_QjQj[ RfFlL ReCfG RbG\\M RcCaH R\\WRY',
	    'PoSL[L RWQ[Q Rf[l[ RSESK RTOTY RWLWX RSE[E[K RgCgZ RlXl[ RhPkT R`W\\[ R]G_L RkGhL RaQ]S R[WRY RbCbQ`W',
	    'Po]ElE RSL[L RWQ[Q RfQkQ R^YkY RSESK RTPTY RWLWX RSE[E[K R^J^[ RbEbP RgEgQ R^JkJk[ R[XRY',
	    'PoSL[L RWQ\\Q RSESK RTPTY RWLWX RSE[E[K RfDjE RjReW][ R]WRY RhG^H RiL`L RiP_Q RcCcLfZk[lW',
	    'PoaJlJ RSKYK Rb[l[ RSESK RSPSY RWKWX RSEYEYK R\\F]I R[M^M^X[[ RfCfLkV ReMbU RWQZQVQ RZXRY RfIdR',
	    'Po_GeG RSKZK RWQ[Q R\\RlR Rf[l[ RSESK RSOSY RWKWX RSEZEZK RgRg[ RkLkO RlXl[ R`W][ RcKfNiO RhJ_N RaCcK RaR`Y R[WRY RhFaG',
	    'Po]HcH RSKZK RfNlN RWQZQ R\\TlT RSESK RSPSZ RWKWY RSEZEZK R]C]O RdPd[ RfDfN RlLlN RkFeI RcM\\N RZXRZ',
	    'PoSKYK RaMlM RWQZQ Rb[l[ RSESK RSPSY RWKWX RSEYEYK RgEgX R[M^M^XaZ R^X[[ R\\E^I RZWRY RjEaF',
	    'PoSK[K RhMkM R]PkP RWQ[Q RSESK RTPTY RWKWX RSE[E[K RdMd[ R`EhEhL RfSlY RaT\\Y RdOfT RdOaU R^L\\M R[WRY R`E_K',
	    'Po]GlG RTK[K RWQ[Q RTETK RTPTY RWKWX RTE[E[K RhQh[ R`W][ RdCeG R`GaJdMkP RiGgJeM^P R[WRY RaR`X',
	    'Po^JkJ RSKZK RWQ[Q RaWhW Rf[k[ RSDSK RSPSY RWKWX RSDZDZK RaQaX RaQhQhV R^DkDkJ R[XRY R^NlNlVk[ R^D^R\\Y',
	    'Po^JiJ RSL[L R^PkP RWQ[Q Ra[h[ RSESK RSPSY RWLWX RSE[E[K R_J_O RaTa[ RhXh[ R^EiEiJ RcQfT RkMjP RiTlZ R^U\\Z R[WRY',
	    'Po`GkG RVSiS R_WjW R][l[ RVPVS RWFWN R[F[M R_S_Z RVPhPhS RXVS[ RYDRJ R`F\\J R`IkN RaC_H RhGeK^N RZYcZ',
	    'Po^GkG RSL[L R]MlM RWQ\\Q RdSkS Rd[l[ RSESK RTPTY RWLWX RSE[E[K ReCeZ R`WdZ R_SbY R_T\\[ R_Q]XRY',
	    'PoSK[K R_MjM RWQ[Q RdSkS Rd[l[ RSESK RSPSY RWKWX RSE[E[K R_D_L RdMdZ R_DjDjL R`VcZ R_TaY R_T[[ R_Q]Z R[WRY',
	    'Po]ElE RSL[L RWQ\\Q R^SkS R[[l[ RSESL RTPTZ RWLWY RSE[E[L RdSd[ RdMeQ RgLjO R^L`Q RjGhL R_G]L ReGcL R\\XRY',
	    'Po\\ElE R^KdK RSKZK RWQZQ R^QdQ RgZlZ RSESK RSPSY RWKWX RSEZEZK R^E^W RdEd[ RhGhZ RlVlZ RZWRY ReV\\W',
	    'PoTK\\K R_PkP RXQ\\Q R_UkU Rg[k[ RTETK RTPTY RXKXX RTE\\E\\K R_K_[ ReCeK R_KkKk[ RkEjI R`EbI R\\WRY',
	    'PodGjG RSK[K R\\LlL RWQ\\Q RSDSK RTPTY RWKWX RSD[D[K R_F_L R_O]T R\\WRY RdCdUaU RiPhUdXaZ\\[',
	    'PoSF\\F R_IlI RVSiS R_WjW R][l[ RVPVS RXCXN R_S_Z R`E`K RgHgN RTNWOiPiS RXVT[ R^M\\N RWW\\Zc[ RiD_E R\\IRJ',
	    'Po\\IkI RSL[L RWQ[Q RSESK RSPSY RWLWX RSE[E[K RfIfNkMkN RhFkJ RaP\\U R`SdXl[ RaC_I RaI_M\\O R`RiRdW\\[ R[XRY',
	    'Po_GkG RSKYK R\\QlQ RWQZQ R^XlX Rc[h[ RSESK RSPSY RWKWX RSEYEYK RbSeV RcMeO R`C]K RZWRY R`KjJjUh[ R`J_P^X',
	    'Po]GkG RSL[L R^QjQ RWQ[Q RSESK RSPSY RWLWX RSE[E[K R^K^Q RdCd[ R^KjKkQ RfSlY RdOfT RbS\\Y RdPbT R\\WRY',
	    'Po_KjK RSL[L R_PjP RWQ\\Q RSESK RTPTY RWLWX RSE[E[K R_F_Z RdCdG R_FjFjP ReUk[ RdPgX RkSfU R\\WRY RfY\\Z',
	    'PoTK[K R^PlP RXQ\\Q R^UlU Rg[l[ RTETK RTPTY RXKXX RTE\\E\\K R^K^[ ReKe[ R^KlKl[ RaGiK R_EkEiGdI R\\WRY',
	    'Po\\HdH RSKZK R[NdN RWQ[Q RiVlV RSESK RSPSY RWKWX RSEZEZK RfDf[ RfDlDlU R[VRY R`C`M]Y RbSdX[Y',
	    'Po]GkG R^JkJ RSK[K R\\OlO RWQ[Q R\\RlR Rd[h[ RSESK RSPSY RWKWX RSE[E[K RiOi[ RaUdX RbN_V[XRY RdCaR',
	    'Po_GhG R^KlK RTK[K RWQ[Q R^UlU Ra[e[ RTETK RTPTY RWKWX RTE[E[K RdCdK ReSe[ RiI_R RjDhJ R`PjPeR R\\WRY',
	    'Po]ElE RTKZK RWQZQ R^ZjZ RTETK RTPTY RWKWX RTEZEZK R^R^[ RdHdP R^RjRj[ RaK]M RfE_L RfJkK R[XRY',
	    'PoSL[L RaOdO RWQ[Q R^UjU R^ZjZ RSESK RTPTY RWLWX RSE[E[K R^Q^[ RdCdO R^QjQj[ RfIkN RjFfI R`J\\N RdDgK R]GaG_M R[WRY',
	    'PobGkG R`HiH R]HfH RSL[L RWQ\\Q RaSkS R]ShS RSESK RTPTY RWLWX RSE[E[K RkLkN RlYk[ RaDcJfMjN R`QcWeYj[ RdOhP ReDiE RiIdL\\M R\\WRY RiTgWbY[[',
	    'Po]GkG RSK[K R`LhL R`QhQ RWQ\\Q R]UlU RSDSK RTOTY RWKWX RSD[D[K R`C`U RiCiU RgWl[ RaW^[ R\\WRY',
	    'Po]IlI RSKZK RWQ[Q R]SlS RSDSK RSPSY RWKWX RSDZDZK ReEeN ReIiMlN RaL\\N RdH_M RcP`VjZ R[WRY RhUdY][ RiD_E',
	    'Po_IlI RTK[K R_NkN RXQ\\Q R_SkS R_YlY RTETK RTPTY RXKXX RTE[E[K R`H`[ RfHfY ReDfG RaD]L R\\WRY',
	    'PodFlF RSL[L R^NkN RWQ[Q R^RkR R]VlV RSESK RTPTY RWLWX RSE[E[K R^J^R RdCdJ RdRd[ R^JkJkR R[WRY',
	    'Po\\FkF RTLZL R]MlM RWQ[Q R]QiQ RcUjU RcZlZ RTETK RTPTY RWLWY RTEZEZK RdCdZ R]JjJjQ R^UcZ R_T\\[ R[XRY',
	    'Po^GkG RSK[K R]MlM RWQ\\Q R_UjU R_ZjZ RSESK RTPTY RWKWX RSE[E[K R_Q_[ RaCaM RhChM R_QjQj[ R\\WRY',
	    'PoTL[L RWQ[Q Ra[h[ RTETK RTPTY RWLWX RTE[E[K RaTa[ RhYh[ RbJeL RcEkL RbG\\M RcSeW RiUkZ R^NiNgT RcCaI R]U\\Z R[XRY',
	    'PoSK[K R^QiQ RWQ[Q Rg[k[ RSESK RTPTY RWKWX RSE[E[K RdJdZ RcRfThV RbT]X R`L`O RgKfO RbC^L RdPaV R\\WRY R`HlHlVk[',
	    'Po]KbK R]PbP RSPSY RSESKXKWX RSEYEYK R]E]W R[EcEc[ RgRk[ RiQeZ RfNjY RkLgX RWQZQVQ RYWRY RdU[W RfGhV RdGkGiV',
	    'PoSKYK RVQZQ R\\TkT RSESK RTOTY RVKVX RSEYEYK R\\O\\[ RaOaZ RfOfZ RcDjJ R_KhJlK RcD\\K RZWRY R\\OkOk[h[',
	    'Po]GjG R\\LlL RSLYL RWQZQ R]UkU RZ[l[ RSESL RSPSZ RWLWY RSEYEYL RcCcL RcQc[ RgNjQ R`N\\R RZXRZ',
	    'Po^GjG R]KlK RSKZK RWQ[Q RSESK RSPSY RWKWX RSEZE[K RdCdK RbO]U RfMkO RaSeXl[ R`N\\P R`RiRdX\\[ R[WRY',
	    'Po^IjI RSKZK R^NjN RWQZQ R\\RlR RSESK RSPSY RWKWX RSEZEZK R^D^N RdDd[ R^DjDjN RdRlY RdR\\Y R[WRY',
	    'Po^JkJ RSLZL R^NlN RWQ[Q R`ZkZ RSESK RSPSY RWLWX RSEZEZK R`T`[ ReJeS R`TkTk[ R^DkDkJ R[WRY R^D^R\\Y',
	    'Po]HdH RSKZK R\\OdO RWQ[Q RfXlX RSDSK RSPSY RWKWX RSDZDZK RfFfZ RfFlFlZ R`G`SdX R`R\\Z R[WRY R^C\\M R`M_W',
	    'PoSL[L RhUkU Re[l[ RSESL RTOTY RWLWX RSE[E[L RdCdG RfLf[ ReLkLkU RlXk[ R]P`T RaV\\[ RcQ_Y R_J]R R_McMaW R[WRY R]G]K R]GlGlK R[QWQ[Q',
	    'Po^IiI RSLZL R\\MlM RWQ[Q RdWgW R_[j[ RSESK RSPSY RWLWX RSEZEZK R`R`[ R_RgRgV RgNlS RhDkH R`N[S RjXi[ R`D\\H ReIiQ RbH^Q RcC`O R[WRY',
	    'Po^IjI RSK[K R^MjM RWQ[Q Rf[j[ RSESK RSPSY RWKWX RSE[E[K R^E^M R^EjEjM R`O[T RbS\\Y ReWb[ RhQdY RcQaV R\\WRY R_QlQj[',
	    'Po]GkG RSL[L R\\NlN RWQ[Q R_ZjZ RSESK RTPTY RWLWX RSE[E[K R_R_[ RdCdG R_RjRj[ RiIgM R`HaM R\\WRY',
	    'Po]GlG RSK[K RWQ[Q R\\TlT RSDSK RTOTY RWKWX RSD[D[K RdQd[ R`LcO RgLkO RdCeG RhIeP RaI]P R[WRY',
	    'Po]GkG RSK[K R]MlM RWQ[Q R\\RlR RSESK RTPTY RWKWX RSE[E[K R`HaK RbDeF RbR_T RaVk[ RcNaS RhHfM R^ViY R[WRY RiRgVeY\\[',
	    'Po]FkF RSL[L RWQ\\Q R^WeW Re[i[ RSESK RSPSY RWLWY RSE[E[K R^Q^X R^QeQeW R\\NkNj[ RcHkK RcHaJ]L RdCcI R\\WRY',
	    'PodFkF R_HcH RSKZK R[MdM RWQZQ R][_[ RSDSK RSOSY RWKWX RSDZDZK R_C_[ RfWb[ RbQcV RfQkZ RiPdY ReLiX RjJfW R\\QZXRY ReHhW RkGhU',
	    'PoSKZK RWQZQ RdRkR RSDSK RSPSY RWKWX RSDZDZK RdNdZ R_HcK RgHkL R`D]L R_Q\\[ RhDeL RZWRY R^UbYjZ',
	    'PoSKZK R]QkQ RWQ[Q RSESK RSPSZ RWKWY RSEZEZK RdNd[ ReRlY RdQhU ReQbT]Y R^I_M RcHdL RjGhL R[WRZ RjD^F',
	    'Po`LiL RTL[L R]PlP RXQ\\Q R`[d[ RTDTL RTPTY RXLXX RTD[D[L RdCeG RdPd[ RiSlY R`S]Y R\\WRY R]G]J R]GlGlJ',
	    'Po\\IlI RSLZL R[[l[ RSESL RSPS[ RWLWY RSEZEZL RbIb[ RfHf[ R_DaH RhCeI R]N_V RjNiV R[XR[ RZRWR[R',
	    'Po^IlI RSK[K RSDSK RTOTY RXKXX RSD[D[K RaMaV RfEfM RaMkMkV RiXk[ RgXjY RdY_[ RXQ[QWQ RfPeX R[WRY R^E^R\\Z RdE^E RjDaE',
	    'PoTL[L R]OlO RWQ[Q R_TjT R`[d[ RTETK RTPTY RWLWX RTE[E[K RdJd[ R]KjKjU RbF]K RbC`H RaFhFeJ R[XRY',
	    'Po^GlG RSK[K ReMlM RXQ[Q RaZkZ RSESK RTOTY RXKXX RSE[E[K RaRa[ ReIeR RaRkRk[ ReCfF R\\WRY R^G^T][',
	    'Po[IcI RSKZK R[PdP RSESK RSPSY RWKWX RSEZEZK RfEf[ R`I_QcY R\\D]G RbD_J RWQZQWQ R_R[YRY RkVhV RfElEiMlRlU R_L^V',
	    'Po_KhK RSLZL RSDSL RSPSZ RWLWY RSDZDZL R]N][ R_PaR R_TaW R^EdDjJ RdD]K ReTh[ RiTl[ RfNeT RjNiT RZWRZ R]NcNb[_[ RZRVQZQ',
	    'Po]FlF R]KlK RSKZK RbOhO RWQ[Q RSESK RSPSY RWKWX RSEZEZK RbHbO ReCdF RhHhO RaTeXl[ R_RjSeX^[ R[WRY R]F]R\\Z',
	    'Po^IlI RSKZK RWQ[Q RaRkR RaVkV RaZkZ RSESK RSPSY RWKWX RSEZEZK RaNa[ RfEfN RaNkNk[ R[WRY R^F^R]Z RdE^E RjD`E',
	    'Po\\FjF R]JjJ RSKZK R[NlN R_VhV R_ZhZ RSESK RSPSY RWKWX RSEZEZK R_R_[ R^RiQi[ RfNlS R`O[T ReJhQ RbI^R RcD`O R[WRY RZQWQ[Q',
	    'PoSL[L R^MkM R^PkP RWQ[Q Rf[j[ RSESK RSPSY RWLWX RSE[E[K R^J^R RdJdQ R^JkJkR RaGhJ R^DkEdG RdSbWaY\\[ R\\WRY R\\UlUk[',
	    'Po]IlI RSL[L R^PjP R^SjS R^WkW R\\[l[ RSESL RTPTZ RWLWY RSE[E[L R^L^S R]EdEd[ R^LkLkS RXQ[QWR R\\XRZ RjDaE',
	    'Po^HjH RSK[K R^LjL R]OlO RWQ[Q RdTjT Rc[l[ RSDSK RSPSY RWKWX RSD[D[K R^D^L RdOdZ R^DjDjL R_UaY R_R]Z R\\WRY',
	    'Po^GhG RSL[L R\\LlL RWQ[Q R`UjU R`ZjZ RSESK RTPTY RWLWX RSE[E[K RaQa[ RcCcL R_RjQj[ RfNiO RjEiH`Q\\S R\\WRY',
	    'Po]FlF R`JhJ RSLZL R`MhM R\\QlQ RWQZQ RfVkV R^ZkZ RSESK RSPSY RWLWX RSEZEZK R^Q^[ R`C`Q RgQgV RhChQ RcQ`W R[WRY',
	    'Po^JkJ RSK[K R\\MlM RWQ[Q RSESK RTPTY RWKWX RSE[E[K R]Q][ R^E^J RbQb[ RcMcQ RdCdJ RgQg[ RkEkJ R]QkQk[i[ R[WRY',
	    'PoaJlJ RSLZL RbMjM RbQkQ RWQZQ RaUkU Rc[l[ RSESL RSPSZ RWLWY RSEZEZL RfCfY RbFjFjM R_R[Z R[E`E\\M`N]X RZWRY R\\Q^WaZi[',
	    'PodHjH RSKZK RWQ[Q RaWhW Ri[l[ RSDSK RSPSY RWKWX RSDZDZK R]N][ RaRaY RdHdN RaRhRhW R`DjDjN R`D^NlNl[ R[XRY',
	    'Po_HjH RSKZK R_LjL R]OlO RWQ[Q Rf[j[ RSESK RSPSY RWKWX RSEZEZK R_D_L R_DjDjL RbU]Z RgRdZ RcR`X R[WRY R`O^SkRj[',
	    'PoSK[K R_MlM RXQ\\Q RbWjW R`WfW Ri[l[ RSESK RTPTY RXKXX RSE[E[K R^Q^[ R_H_M R^EeEeV R_HlHlM R^QlQl[ RhSjX R\\WRY RkDcD',
	    'Po^ElE RSK[K R`MjM RWQ\\Q R^UlU R^ZlZ RSESK RTPTY RWKWX RSE[E[K R^P^[ R`H`M RePeZ R`HjHjM R^PlPl[ R\\WRY',
	    'Po[KcK RVQZQ RSPSY RSDSKXKVX RSDYDYK R`E`[ R`OcS RhTk[ RfScZ R_K[U RkIjO RdIcO RcD\\F RZWRY RhCgOiW RgMfV',
	    'Po]GlG RTK[K RdLiL R_OkO RXQ[Q R]SlS RTETK RTPTY RXKXX RTE[E[K R_D_O RdCdK RdOd[ RiCiK ReTkZ RcT]Z R\\WRY',
	    'PoSL[L R^MlM RWQ[Q R`UlU Rj[l[ RSESK RSPSY RWLWX RSE[E[K R`P`[ RdPd[ RhPh[ R^HlHlM R`PlPl[ R\\XRY R^E^R\\Z RjC_E',
	    'PoSK[K RbOeO RXQ[Q R\\SlS RSDSK RTOTY RXKXX RSD[D[K RdPd[ RfHfN RdRkZ R]IkIjM RdR]Y RaFhI R^DiEdG RdIaL\\N R[WRY',
	    'Po]FlF RSK[K R]MlM RWQ[Q R`ZkZ RSESK RSPSY RWKWX RSE[E[K RaRa[ RaCaJ RhChJ R_SkSk[ RaQ\\U RdJ_S R[XRY',
	    'Po]FlF RSLZL RWQ[Q RSESK RSPSY RWLWX RSEZEZK R]K]O R^P^X RdCdF RdNd[ R]KlKlO RaGaK RhGgK R[WRY R^PjPjXgX',
	    'Po]GlG RSLZL R_SiS R_WiW R][l[ RSPSZ RSESL RWLWY RSEZEZL R_O_W RdDdM R_OiOiW RdGhKlM RdG`L\\M R[XRZ R[RWR[R',
	    'PobIjI RSLZL RbNjN RWQZQ Rb[l[ RSESK RSPSZ RWLWY RSEZEZK RbEbW RbEjEjN RePjV R[M^M^X[[ RjPhS R\\E^I R^Xd[ RfUaW RZWRZ R^M[M',
	    'PoUHhH RVKhK RRNlN RXTfT R_WhW R\\[l[ RSESH R_T_[ RSEkEkH RXVT[ RWQfQlS RZGZNWQRS RXOXT ReGdNgPfT RYZa[',
	    'PoSLZL RWQ[Q R^TbT RfTkT R^ZkZ RSESK RSPSY RWLWX RSEZEZK R^O^[ RcGdK ReOkOk[ RiFlK R_E\\L RcM^O R[WRY RiC^E',
	    'Po]GlG R^KkK RSL[L R\\OlO RWQ\\Q R`SkS R^ZlZ RSESK RTPTY RWLWX RSE[E[K RdGdO ReSeZ RaDaG RiCgG R`R\\Z R`O]X R\\WRY',
	    'PoSL[L RWQ[Q R]RkR R\\UkU RSESK RSPSY RWLWX RSE[E[K RgNkR RhFkI RfK`Q RbI_L RcFcI R^LcN ReVhYl[ R_E\\J RdSbW`Y\\[ R\\WRY RiC^E',
	    'Po_HkH RTL\\L R_LkL RXQ\\Q R`[d[ Rh[l[ RTETL RTOTY RXLXX RTE\\E\\L R_E_L R]OdOd[ R_EkEkL RfOlOl[ RaP]S RbT]Y RiPeT RkSeY R\\WRY',
	    'PoSK[K R^OjO RWQ[Q R^RjR R_ZjZ RSDSK RSPSY RWKWX RSD[D[K R^L_U_[ R^LjLjR R_UjUj[ RdCjI RaIhHlI RdC]J R\\WRY R^P]Y',
	    'Po^IjI RSLZL R_NkN RWQ[Q R]RlR RSESK RSPSY RWLWX RSEZEZK RaRaZ RbKbR RgKgR R^DjDjI RdRgY RjTfWiZl[ R[WRY ReY^[ R^D]R\\Z',
	    'Po_HkH RSL[L RaMjM RaQjQ RWQ[Q RaTjT R]VlV RSESK RTPTY RWLWX RSE[E[K R]L]V R_C_H RaJaT RaJjJjT RgXl[ RaY[[ R[WRY RiD^E',
	    'PoeHkH RSLYL ReRkR RSESL RSOSY RVLVX RSEYEYL ReFeR R`KkKkN RhTh[lZl[ RWQYQVQ RYWRY RcTaZ_[ R\\E\\Q[X R`K`S^Y RiD_E RgNaO',
	    'Po]FaF RgFkF R]IaI RhIkI RSL[L RWQ[Q R_RiR R_ViV Rf[i[ RSESK RSPSY RWLWX RSE[E[K R_N_[ R_NiNi[ RfIjM RbI]N RdDgK RdDaK R\\WRY',
	    'Po^HiH R^KiK RSKYK R\\OkO RWQZQ RSESK RSPSY RWKWX RSEYEYK R^E^O R\\EkEiO ReUk[ R]UbY RaU[[ RdRgX R\\RbR_X RcRkRiVfYb[ RZWRY',
	    'Po\\FlF R\\JkJ RSKZK Rf[j[ RSESK RSPSZ RWKWY RSEZEZK R]N][ RaCaH RgCgH R]NkNk[ RfJgSiV RcScU RZXRY RZQVQZQ RbJbOaV RfMeW',
	    'PoSK[K RhNkN RXQ\\Q R^UkU R^ZkZ RSESK RTPTY RXKXX RSE[E[K R^E^N R^P^[ RePeZ R^PkPk[ ReElElM RbHcK]M RcD^E R\\WRY RgEfM',
	    'PoSE_E RbHlH RTK^K RRM_M RVTiT R_WjW R\\ZlZ RTGTK RVQVT RYCYO RTG^G^K R`T`Z RhHhO RVQiQiT RXVUZR[ RYYbZ RjDbEbJaN R\\IYIUI',
	    'Po]HkH RSLZL R\\PlP RWQ[Q R\\SlS R]WkW RSESK RSPSY RWLWX RSEZEZK R_L_S RdDd[ R]D]LjLhS R]DkDkL R[WRY',
	    'Po\\FjF R]JiJ RSKZK R\\MkM RVQZQ R^VbV RdVhV R^ZhZ RSESK RSPSY RVKVX RSEZEZK R^Q^[ RdRiQi[ RfMkS R`N[S RbH^Q RcD`N RZWRY RbQ\\R',
	    'Po]FlF RTK[K R]OkO RWQ[Q R`TdT Rf[l[ RTETK RTPTY RWKWX RTE[E[K R`Q`[ RaFaO RfQf[ RfFfO R]JkJkO RlXl[ RcCdF RkSfU RdZ_Z R[WRY R]F]S[[',
	    'PoSKZK RSESK RSOSY RWKWX RSEZEZK ReDjDjM R]G_H ReGgH RdMfPlR RbI\\K RjIdK R]DbDcLaP[R RcR^T RfT_X RZWRY RhWdY][ RZQVQZQ',
	    'PoUFkF R[H`H RWKaK RYO[O RVTiT R_WjW R][l[ RVQVT R\\F\\O R_T_Z RVQiQhT RkMkP R^L`N ReLhO RiHcO RXLVN ReDiD RXVVYR[ RcCeM RWX\\Zb[ RUFUKTO',
	    'PoSLZL RWQ[Q R_RiR R^VkV R\\[l[ RSESL RSPSZ RWLWY RSEZE[L R]H]L R_L_R RdCdH RdRd[ R_LiLjR R]HlIlL RiDhG R_D`G R[XRZ',
	    'Po[GlG RSKZK RiLlL RVQZQ RgYiY RSESK RSPSY RVKVX RSEZEZK R\\N\\Q R^R^Y RdOd[ RfCfK RiCiK R^RjRjY R\\NkNkQ R[WRY RbCbKfK R_E^K',
	    'PobJlJ RSKZK RWQ[Q RbTkT RbZkZ RSESK RSPSY RWKWX RSEZEZK R]F]J R_L_[ RbOb[ RdCdF RfJeO RbOkOk[ R]FlFlI R`I\\R R[WRY',
	    'PofGkG RSLYL R`LlL RVQZQ RfSkS RSESK RSPSY RVLVX RSEYEYK R^L^[ RbFbL RcWfZ R^C\\I R_I[Q RZWRY RgCfZl[ RbPaZ',
	    'PobDlD RSKZK R]KaK RcMkM RWQZQ RcQkQ RcVkV RSESK RSOSY RWKWX RSEZEZK R]D]W RcIcU RgEfI RcIkIkU RhXl[ RdWa[ RaU[W RZWRY',
	    'Po]EkE R^HjH R\\JlJ RSK[K R^PjP RWQ\\Q R^TjT R^WjW RSDSK RTPTY RWKWX RSD[D[K R^M^W RdCdJ R^MjMjW RgYl[ RaX[[ R\\WRY',
	    'Po]GlG RSLZL R]MlM RWQ[Q RbShS RSESK RSPSY RWLWX RSEZEZK RbIbS RcWc[ RdCdG RhIhS RfVg[ RiVl[ R`V_[ R\\WRY R]G]S\\Y',
	    'PoSLYL RbQlQ RVQZQ R\\Q`Q Rf[j[ RSESK RSPSY RVLVX RSEYEYK R`C`[ RjMj[ RcIgL RjIdO RfEbI RdSfW RdGiI ReFkFhK R]E]LaM RZWRY R]S\\[',
	    'Po\\FlF RSKZK R_OiO RWQ[Q RaXhX Rh[k[ RSESK RSPSY RWKWX RSEZEZK R]K][ RaSaY RdCdF RdKdS RaShShX R]KlKl[ RaGaJ RhFgJ R[WRY',
	    'Po\\FlF R`JhJ RSLZL RWQ[Q Rg[k[ RSESK RSPSY RWLWX RSEZEZK R]N][ R`C`J RdJd[ RhChJ R]NkNk[ RaQbW RhQiW RgQfW RaQ_W R[WRY RaQ_P RgQeP',
	    'PoSKYK RVQZQ RfSkS RSDSK RSPSY RVKVX RSDYDYK R^L^[ RcHeJ RhHlL RbTdY R^C\\I R_J[R RdDaL RiDgK RZWRY RgNfZl[ RbPaZ',
	    'Po`FkF RSKYK RaMkM RWQZQ RaQkQ R`UlU Rb[l[ RSESK RSPSY RWKWX RSEYEYK RaJaQ RfCfY RaJkJkQ R[E^H R[M^M^X[[ R^Xc[ RZXRY',
	    'Po]HlH RSKZK R\\ObO RfOlO RWQZQ R\\TbT RfTlT RSESK RSPSY RWKWX RSEZEZK RdCdH RkEkI R]E]I RZWRY R\\KbKbZ_Z RfKlKl[i[ R\\K\\T[Z RfKfTeZ',
	    'Po]FlF RTK[K R^LkL R^PkP RXQ[Q R_VjV R_ZjZ RTETK RTPTY RXKXX RTE[E[K R^I^P R_S_[ RbCbP RfCfP R_SjSj[ R^IkIkP R\\WRY',
	    'PocJkJ RSKYK RWQZQ RSESK RSPSY RWKWX RSEYEYK R^JbK RjDkH R]M`P\\T RePaT RaWa[ ReWf[ RiVk[ R]W[[ RgCgJjS R^C\\L R^GbH`Q RZWRY RgFeP',
	    'PobGlG RVTiT R_WjW R][l[ RTFTO RVPVT RYCYN R_T_Z RVPiPiT R\\D_G RXVS[ R[I\\L RcHgLlN RVIVM RcCaH RiHeL`N RUDRG^G^N\\N RWW\\Zc[',
	    'Po]IjI RSKYK R[LlL RWQZQ R]QiQ R]TiT RSDSK RSPSY RWKWX RSDYDZK R]E]I RcCcK R]NiNiW R]EiEiI R]N]WhXl[ RZWRY R`YY[',
	    'PoTK[K RcNeN RWQ[Q RaYhY Rh[k[ RTDTK RTOTY RWKWX RTD[D[K R]P][ RaUaZ ReGeN RaUhUhY R]PkPk[ RfQiT RbQ_T R]HlHiK RbFhH R^DjEeH\\M R[WRY',
	    'PoaKgK RTL[L RWR[R R_TiT R\\[k[ RTETL RTPTZ RWLWY RTE[E[L R_O_T R_OiOiT RgIlN RiDfG RkHhJ R]H`J\\N R`VaZ ReDiL RgVfZ R^EbE_K R\\XRZ',
	    'PoSKZK R]OjO RWQ[Q R_ViV R_ZiZ RSESK RSPSY RWKWX RSEZEZK R^H^O R_R_[ RdIdO R_RiRi[ R\\JkIjO R_F[J R`KaN RbEgDjJ RgKfN R[WRY',
	    'Po\\EkE RSLZL R]MjM R\\PlP RWRZR R_WhW R_[j[ RSESL RSPSZ RWLWY RSEZEZL R]H]M R_R_[ RbEbM RfEfM R^SiShW R]HjHkM RfPjT RcN]T RjYj[ R[XRZ',
	    'Po\\JlJ RSLZL RWQ[Q R^VjV R^ZjZ RSESK RSPSY RWLWX RSEZEZK R^R^[ RdDdP RdRdZ R^RjRj[ R_G`I RdIkP RdJ]Q RiFhI R[WRY RiC^E',
	    'Po\\ElE R]HdH RSLYL ReMlM R^RdR Rg[j[ RSESK RSPSY RWLWX RSEYEYK R^L^R R^T_W R^LdLdR RjGj[ RfPhU RcTbY RYWRY RYQVQ RdX\\Y R\\E\\RZ[',
	    'Po]FkF R\\KlK RSL[L R^QjQ RWQ[Q R^TjT R]XjX R[[l[ RSESL RTPTZ RWLWY RSE[E[L R^M^T RdCdF RdMd[ R^MjMjT RhFhI R`GaJ R[XRY',
	    'PoSLZL RfNjN R^PjP RWQ[Q R^SjS R\\VlV Rc[g[ RSESK RSPSY RWLWX RSEZEZK R^J^S R`HgHfM RgSg[ R^JjJjS R_WaY RbEhElI RbHbM`O R_F\\I R[WRY',
	    'PoSKYK R\\Q`Q RWQZQ RiQkQ RcTjT RSESK RSPSY RWKWX RSEYEYK R[M`LaP RdLhLiQ RgXj[ R\\G^I ReGjL RhEfG RjHgI RiUdZ R`GZM RdDfI R\\DaD_I RcVgXb[ RZWRY R_[\\[ R\\Q[U`U_[ RcLcQ',
	    'Po[GdG RdJlJ RSLZL R\\QbQ Rh[l[ R\\[_[ RSDSL RSPSY RVLVX RSDZDZL R\\J\\Q R_C_G R_Q_[ R\\JbJbQ RhChZ RjEkH RbTbW RfScZ RgNdY RWQZQVQ R\\S[XRZ RlWl[',
	    'PoRE_E R`FlF RTJ]J RVSiS R_VjV R][l[ RTGTJ RTG]G]J R_S_Z RVOiOiS ReMkLlM RdI`M RXVS[ RUKSM R[K^L RhDjD RVOVS ReDcK RYJXMVM RZYcZ',
	    'Po\\DlD R]KdK RVQYQ R^TdT RSPSY RSESKWLVX RSEYEYK R^N^V RdNdU R^G_J RiUl[ RhKhSeZ RZXRY RfKkKkQ RhNjX R\\D\\RZZ RcGaKaT`Y RfGeP RhOgX',
	    'PoTDkD RVHaH RWL_L RVTiT R_WjW R\\ZlZ RVQVT RWJWM R`T`Z R_J_M RVQiQiT RXFYH RfKjO ReLbO R\\LXP RcHjHjK RdFaK RXVUZR[ RfHeM RYYbZ RTDTJSP R[H[N',
	    'Po\\HlH RSKYK RWQZQ RdQlQ RdWlW RSESK RSPSY RWKWX RSEYEZK RdCdN ReSdW RiOi[ R^D`F RiDgG R^UaV\\[ RdHgKlM R^O\\T R^RbQ_X RcH`K\\M RZWRY',
	    'Po`HhH RSLZL R_OiO RWQZQ R_[b[ Rg[k[ RSESL RSOSY RWLWX RSEZEZL R_K_O R\\QbQb[ R_KiKiO RdQkQk[ R_R\\U RaU\\Z RhRdU RjTeY ReElH RcCaF\\H RZWRY',
	    'Po_FjF R]JlJ RTL[L R]McM ReMlM RWQ[Q RdQlQ R\\TlT Rg[l[ RTETL RTOTY RWLWX RTE[E[L R`K`Q RdCdJ RgTg[ RhKhQ RlXl[ R`Y\\[ RaT`Y RcP]Q R\\WRY',
	    'Po\\HlH RSKZK R`OhO RWQ[Q RaXgX RSESK RSPSY RWKWX RSEZEZK R]Q][ R`J`O RaTaY RaTgTgX R_KhKhO RbG]L RfHhKlL R]EcDaJ R[WRY R]QjQj[g[ RiCaD',
	    'Po\\IlI RSLZL R]MkM R^QjQ RWQ[Q R\\UlU RSESK RSPSY RWLWX RSEZEZK RbCbI RfCfI R^D_G RiDhG R`JaM ReVhYl[ RhJfM RdMcU`Y[[ R[WRY',
	    'Po]GlG RSKZK R_PiP RWQ[Q R_SiS Ra[d[ RSESK RSPSY RWKWX RSEZE[K R_L_S RdSd[ R^MjLiS RhVkZ R]I`K RfHlN RjIhK R`V\\Y RbI\\N RdC`K R[WRY',
	    'Po[GdG RSKYK R\\NcN R\\QcQ R\\[_[ RSESK RSPSY RWKWX RSEYEYK R\\J\\N R`S`[ R_D`G R\\JcJcN RbR_S RgWc[ RgSkZ RiQfY RYWRY RfJhW RfJkJhV RgCdO RYQVQ RaU[V RdU]V',
	    'PoSKZK R_PkP RWQ[Q R^XlX RSESK RSPSZ RWKWY RSEZE[K R^S^Y ReJe[ R_LkLkP R^SlSlY R]F]GlFlI RdCeF RgHlJ RcH^J R_L_P R[XRY',
	    'Po\\HlH RTKZK R^PjP RWQ[Q R^SjS R^WjW RSPSY RTDTK RWKWX RTDZDZK R^M^V RjMjV R_DkDkK RgXl[ RaX\\[ R^KhLjM RaM^M R[WRY R_D^K ReDdK RfMcM RbMeM',
	    'Po_HhH RSKZK R\\PbP RePkP RWQ[Q R\\SlS RSESK RSPSZ RWKWY RSEZEZK R\\K\\P R_D_H R\\KbKcP RdQd[ ReKeP R_DhDhH ReKkKkP RdSjY RdS]Z R[XRY',
	    'PoaFjF R`JlJ RSLYL R`NkN RaRjR R`UlU Ra[l[ RSESK RSPSY RVLVX RSEYEYK ReCeJ RfNfY R[J^M R[Q^Q^X`Z R\\D^G RiKhM R^WZ[ RbKbM RVQYQVQ RZWRY',
	    'PodGlG RSKYK R\\LbL RcNlN RVQZQ RdTlT R]XbX RSESK RSOSY RVKVX RSEYEYK R\\E]R][ R\\EbEbK R]QbQbX ReIeM RhChG RhNh[ RjIjM RZWRY R\\N[Z',
	    'Po_EkE RTH\\H R_IlI R_LkL RVN]N RVTiT R_WjW R\\ZlZ RTDULVN RVQVT RTD\\D\\H R`T`Z ReIeO RVQiQiT RXVVZR[ RVK]K]O RTHSO RWX\\ZbZ RhGhI',
	    'Po]IlI RSK[K RWQ[Q R^ThT R]YcY Rg[k[ RSESK RSPSY RWKWX RSE[E[K R]E]I RbEbI RcNcX RgEgI R^PhPhT R]ElElI RgViY R`J\\N R^P^T R[WRY R^MlMlVk[ RhXaX',
	    'PodEjE RbKhK RTKZK RdNiN R`PlP RWQ[Q RTETK RTPTY RWKWX RTEZEZK RdCdM R^HkHkJ RhVlY ReP_S ReT_V RdRgUdW_Z RjSeTfY R[WRY Re[a[ R^H^T\\Z',
	    'Po\\FlF R^KhK RSLZL R\\OlO RWQ[Q R`WjW R`ZjZ RSESK RSPSY RWLWX RSEZEZK RaRa[ RaCaH RcHcO RgCgH R^SjSj[ RkIdP RfPjQ RcQ[T R[XRY',
	    'Po[JbJ RSKYK R[NbN RVQYQ R[QcQ RSESK RSPSY RVKVX RSEYEYK R[F[N R^C]FbFbN RgRjZ RiQeZ Ra[^[ RfMiX RYXRY ReJhV ReJkJhV R^Q\\Y RfCdO R]UbUa[',
	    'PoSEkE RVKhK RWPfP RVUiU R_XjX R\\[l[ RTMTR RVRVU RVGVK R_G_O R_U_Z RVGhGhK RVRiRhU RXXR[ RdIhI RYIVI R_I[I RbI_I RTMjMjQgQ RYYb[',
	    'Po\\EkE R]HjH RSLZL R^MiM R]PjP RWQZQ RcUlU R\\YaY Rf[i[ RSESK RSPSY RWLWX RSEZEZK R\\T\\Z R\\TaTaY RcCcH RcMdO R\\RkRj[ RdWfY R\\KlKkM RZWRY',
	    'Po\\FlF RSLZL RfOiO RWQ[Q R^WjW RSESK RSPSY RWLWX RSEZEZK R^P^X RdJdO RdCdF RgIgN R^RjQj[ RfGeI RbGbI RiIlO R^I\\O RkIfI R\\IaI`O_N R[WRY',
	    'Po\\JlJ RSKYK R[OlO RdRlR RdXlX RSDSK RSPSY RVKVX RSDYDYK R^D^O RbFbO RdTdW ReFeO RiPi[ R]FjFiO R^Q[U R]UaV\\[ R_D[G R]RbR_X RYQVQ RYWRY',
	    'Po]HcH RfHkH R]KcK ReKkK RSLZL R^PkP RWQ[Q R^SkS R^VkV R^ZlZ RSESK RTPTY RWLWX RSEZEZK R_N_[ R]DcDcK ReNeZ ReDkDkK R^O[S R[XRY',
	    'Po\\DlD RSKYK Rh[k[ RSESK RSPSY RVKVX RSEYEYK R]I][ RdDd[ R[KkJk[ R_TbW RgFlJ R_MaO^Q RaS_X RhLgP RfMhPeQ RhSgX RfThWeY R_F[J RYQVQ RZWRY',
	    'Po\\ElE RSKYK RaOhO RWQZQ RaRhR RaThT RaXiX Rh[k[ RSDSK RSPSY RWKWX RSDYDYK R]M][ RaNaY RaCaG ReMeW RgCgH RaJkJk[ R]I_L RaN^Q RZWRY',
	    'PoSLZL R\\PjP RVQZQ RSESL RSOSY RVLVX RSEZEZL R\\J\\P R]R]Z RcRcZ R\\JjJjP R\\F_H RbFdI RgFjI R_C\\F RdCbF RiCgF R`KgO ReSgS R_SaS ReVgW R_VaW RgK_O RZWRY RaZZZ RgYaZ RiSkZl[',
	    'PoSLYL RaMdM R[PdP R[RdR R[UeU RSESK RSPSY RVLVX RSEYEYK R[K[R R^H^M R]HbHaM R[KdKdR RgEg[ R^EbDeH R\\FZH R_WcY R]YY[ R_R^Y RYQVQ RZWRY RgElEiMlSlWiW',
	    'PoaKeK RSLZL RfPlP R[P_P R[SlS RSESL RSOSY RWLWX RSEZEZL R^M_Q RbCbFaPdPc[ RaFeFeP R[H^K RfHiK RkLkQ RcRhXlY RjGgP R]D\\I R_H[P RhDhI RWQZQWQ RcS^XZXRY',
	    'PoeGlG RSLZL R^QjQ RWQ[Q R^TjT R^WjW RSESK RSPSY RWLWX RSEZEZK R^M^W R]GbGaL RiGiL R^MjMjW RgXl[ RaY[[ R[XRY RbD]E\\L RjDeEdK',
	    'Po\\ElE R`JjJ RSLYL R`MjM R_OjO RVQZQ R^RlR RgUlU R_WjW R][l[ RSESK RSPSY RVLVX RSEYEYK R`H`M RdCdF ReHeR ReUe[ RgRgU R`HjHjM RcR`U RZWRY R\\E\\R[Z',
	    'Po\\FlF R^IeI RSLYL R^LdL RfMlM RWQYQ R^SdS Rg[j[ RSESL RSOSY RWLWX RSEYEYL R^O^S R_U_X RaGaL RbTbY RdCcF R^OdOdS RjGj[ RgPgU RYWRY ReX]Y R\\F\\RZZ',
	    'PoSLZL R]M`MlN R\\[l[ RSESL RSPSZ RWLWY RSEZEZL RlDl[ RiGeL RbT]Y RbG]L RbKbM RiTeYjXjZ RgPeU R`D^H R_P]U RgDeH RbH]H RiHdH RZQWQ R[XRZ RiTdT RjLdL RbT]U RbWbY]Y',
	    'PoeDkD RYGeHkH RZKcK RXMdM RYPdP RVUiU R_WjW R\\[l[ RVRVU RWHWQ R]D]G R`U`Z R_K_Q RZEbEcG RZIZJcIcK RVRiRiU RWGTJ RXWVYR[ RWESF RiHhPfP RYYb[',
	    'Po[FcF ReFlF RSKYK R[LcL ReNkN ReQkQ R\\RbR ReTjT R\\VbV ReWkW R_[b[ RSESK RSOSY RVKVX RSEYEYK R\\O\\[ R\\G]K R_C_F R\\ObOb[ ReCeIkIkN ReNe[lZl[ RaG`K RVQYQVQ RYWRY',
	    'Po\\HcH RfHlH R\\KcK RfKlK RSLZL R`PiP RWQZQ RaShS RaUhU RaXiX Rh[l[ RSESK RSPSY RWLWX RSEZEZK R]E][ RaOaZ R\\EcEcK ReNeX RfEfK RfElEl[ RaM_S R[WRY',
	    'Po`DlD RSLYL R`NlN Rb[l[ RSESL RSPSY RWLWX RSEYEZL R`G`K RdDdJ RcPcWjWjX R`GkGkK R[P_P^XaZ R\\J^M R\\D^G RgNkS ReN`S R^WZ[ RgDgJdJ RZQVQ R[WRZ RaRhQhUeT R^P[P',
	    'PofDkD RYGeHlH RZKcK RYLeL RbQdQ RVUiU R_XjX R\\[l[ RVIVQ RVRVU R]C]G R_L_Q R_U_Z RZEbEbG RZHZJcHcK RZNZOcNcQ RVRiRhU RiHiQ RWCSG RWGRL RXXR[ RYYb[',
	    'PodFkF R[FbF R[IbI RiNlN RSPSY RSESKXKVX RSEYEYK R]P]V RdIjIiN R]PjPjV ReXk[ R\\D[G R_C^I[N ReDdG RhCgIeM RbL_M RWQZQVQ RZWRY RaYZ[ R_F`M RcRbX',
	    'Po]HcH RfHlH R]KcK RfKlK RSLZL R_NjN RWQZQ R`UiU Ri[l[ RSESK RSPSY RWLWX RSEZEZK R]D][ R]DcDcK RdMdZ RfDfK RfDlDl[ RdTiY RdT_Y R`Q`U R`QiQiU R[XRY',
	    'PoaDeD R`FeF RaIeI RaKeK RVQYQ RaQeQ RSOSY RSESKWKVX RSEYEYK RaMaQ RaMeMeQ R]C]M_P RiCiMkQ R[G[K RgGfK R^T`WeYl[ R_G]J[Q RZWRY R[TiTeWaYZ[ RkGiJhP',
	    'Po\\FlF RSKYK ReLkL R\\NkN RWQZQ R]QkQ R\\TlT RSESK RSPSY RWKWX RSEYEYK R]H]LaLaT R]HbHbL ReHfLgT ReHkHkL RcCdF ReUhYl[ R`V_[ RdT\\X RkVfW RZWRY ReZ_Z',
	    'PoRElE R_IkI RTL\\L R^LlL R_OkO RVUhU R_XjX R\\[l[ RVRVU RZCZG RTI\\I\\L RTN\\N]Q R`U`Z ReLeQ RTITQYRhRhU RbJbL RdGfI RhJfL RdDdG RXWVYR[ RYYb[',
	    'Po]DjD R_JhJ RSKYK R\\LjL R\\RaR ReRjR R\\UaU ReUjU RZYaY RdYkY RSDSK RSPSY RVKVX RSDYDYK R\\O\\X R_D_L R[ObOb[ ReOeX R_GhFhN RdOkOj[ RZXRY RVQZQ',
	    'Po]GcG RfGlG R]JcJ RfJlJ RSKZK RbOgO RWQZQ RbQgQ RaZcZ RgZiZ Rj[l[ RSESK RSPSY RWKWX RSEZEZK R]D][ R`M`Q R]DcDcJ R_TcTcZ RfDfJ R`MhMhQ ReTiTiZ RfDlDl[ RbT_V RbW_Y RhTeV RhWeY R[XRY',
	    'Po\\GbG ReGkG R\\JbJ ReJkJ RSKYK R\\LbL ReLkL R]QkQ RWQZQ R]SkS R]WkW R]ZlZ RSESK RSPSY RWKWX RSEYEZK R\\D\\L R^P^[ R\\DbDbL ReOeZ ReDeL ReDkDkL R^M\\S RZWRY',
	    'Po\\FkF R\\KbK RSKYK RdLkL R\\OaO RcPlP RWQZQ R[RbR RdVkV RdZkZ RSESK RSPSY RSEYEYK R_I_[ R`C`H RdSd[ RfJfP RgCgH RiJiP RdSkSk[ R^RbV R_Q[X RWKXXRY',
	    'PoSKZK R]NbN ReNjN R]QjQ RWQZQ RSDSK RSPSY RWKWX RSDZDZK R^K^Q R]S][ RcRc[ RdKjKjQ R\\F\\GkElH RbCdE ReSgT R_SbS ReVgW R_VaV RfHjJ RbG]I RaZ\\Z RZWRY RhZbZ RcK]K RiSj[',
	    'Po\\FbF ReFkF RdIlI RSKYK RiMlM RWQZQ R^QjQ R^TjT R^WjW RSESK RSPSY RWKWX RSEYEYK R^O^W R^OjOjW RfXl[ R\\D[G R_C^I\\M ReDdG RfIeM R\\IaIaM_M RaY[[ RhChGiJiM RZWRY',
	    'Po\\DcD ReDlD RSLZL R]MlM R\\TkT R_WdW RSESL RSOSY RWLWX RSEZEZL R]G]K R_T_[ R_H_J RaMaT R]GbGbK ReGeK RhHhJ ReGkGkK R]PkPkT RfMf[lZl[ RkVfW RZQVQ RZWRY RdZ][ R]M\\T[Z',
	    'Po\\FkF RSKYK RaMfM RaOfO RVQYQ RSESK RSPSY RVKVX RSEYEYK R\\J\\P RaHaO RhHhO R`DbF RgDeF RgQlS R^H_O[P RaQ\\S RaU\\W RaWeYk[ RcFbHfHfL RaKfKfN RhHkHkLgL RkPhP RYXRY R`UhUfXcY[[',
	    'Po\\HbH ReHkH RSKYK R]OjO R]QjQ R]SkS R\\VjV RSESK RSPSY RVKVX RSEYEYK R^L^S RdLdS R^M[P R\\D]J]P ReDeJhJ R]J`J R_WdYk[ R]F`F\\F R_FbF^F R\\DbDbJ_J ReDkDkJgJ RZWRY RgFkFhFeFiF RZQVQZQ ReHdL RiWdY[[',
	    'Po\\ElE R\\KbK ReKkK RSLZL R\\NbN ReNkN R`ThT R`WhW R`YiY RSDSL RSPSY RWLWX RSDZDZL R\\I\\[ RaQaZ RaCaG R\\IbIbN RePeY ReIeN RgCgG R`Q^T RWQZQWQ R[XRY ReIkIk[h[',
	    'PoWJgJ RWOgO RRTbS R`[g[ RWFWS RWFgFg[ R_D]F RlMfR\\XR[',
	    'PoTJ\\J RaKlK RTN\\N Rd[j[ RX[\\[ RTFTR RXCWF RTF\\F\\[ RaDlDlL R^OVWRY RaK`QlQk[ R\\QRR',
	    'PoTJ\\J RTN\\N Rf[l[ RX[\\[ RTFTR RXCUF\\F\\[ R_H_L ReCeNfZ RbV^[ RlWl[ RVWRY R]PTX RdQ`Y R_HlHlL R\\QRR ReHcV',
	    'PoTJ[J RTN[N Rd[h[ RW[[[ RTFTR RTF[F[[ RbW^[ R\\PTY RbEhElN RWCWF RaG^N RcObX R[QRS R_OjOjUi[',
	    'Po`ElE RTJ\\J RTN\\N R`ZlZ RX[\\[ RTFTR RXCUF\\F\\[ R`E`[ RVWRY R]PTX RdKgOjV RjHdU R\\QRR',
	    'Po]IlI RTJ[J RTN[N R`ViV RW[[[ RTFTR RTF[F[[ RdCd[ R`S]V R\\QSY RdHjU RWCWF RdI_U R[QRR',
	    'PoTJ[J RfNjN RTN[N R]RlR RW[[[ RTFTR RTF[F[[ RdOd[ RfTlZ RcT]Z R[PSY RWCWF RaF_M R^DgDgHkHjN R[QRR',
	    'PoTJ[J RhMlM RTN[N R^PkP RX[[[ RTF[F[[ ReMe[ RaEhEiL RgTlY RbU]Y ReOgT RWCVF RdOaV RTFSR[RVWRY RaE`K',
	    'PoTJ\\J RaLkL RTN\\N R`ZlZ RX[\\[ RTFTR RTF\\F\\[ R`R`[ RaDaL RaDkDkL R`RlRl[ RXCWF R]OVWRY RfLeR R\\QRR',
	    'PoTJ[J R_KjK RTN[N R_PjP RTF[F[[ R_F_[ RdCeF R_FjFjP ReUkZ RdPgW RWCWF RTFSR[RVWRX RkSfU RX[[[X[ ReY^Z',
	    'Po^EkE RTJ[J RTN[N ReNiN Rb[h[ RW[[[ RTFTR RTF[F[[ RbSb[ RhXh[ RcK_O R[QSY RiTlY RbPeR RWCWF R`G^K R^T]Y ReEcL R[QRR RkFjN',
	    'Po_IjI RTJ[J R_NjN RTN[N R]QlQ RW[[[ RTFTR RTF[F[[ R_D_M ReDe[ R_DjDjM RfTlY RcT]Y R[QSY RWCWF RdPcT R[QRR',
	    'Po]IlI RTJZJ RTNZN R]SlS ReEeN RdHhLlN RaL\\N RTFSRZRSY RdH_M RcP`WjZ RWCUFZFZ[W[ RhUdY][ RiD_E',
	    'Po^FlF RTJ[J RTN[N R`WfW RX[[[ Re[j[ RTF[F[[ R`Q`X R`QfQfV R]NkNj[ RVWRY RTFSR[RUX RdHkL RWCWF ReHbK^L ReCdJ',
	    'PoTJ\\J RTN\\N R_QiQ RY[\\[ Rg[k[ RTF\\F\\[ RdJdZ RdRgU RbT^X R`LaO RgKgO RXCWF RbC^L RdPaV RTFSR\\RWWRY R`HlHlVk[',
	    'PoTJ\\J RTN\\N RcVhV RW[[[ Rh[l[ RTFTR RTF\\F\\[ R_L_[ RcOcW RfCfK RcOhOhV R_LlLl[ R\\PTX R_EaI RlDiI RXCWF R[QRR',
	    'PoTJ[J RTN[N R_RkR R]ZlZ RX[[[ RTFTR RTF[F[[ R^F^J ReCeG ReReZ RgFgNlMkN R^FlFlI R[PSY RWCWF R`N^O RbGaM R[QRS',
	    'Po`EkE R`HjH RTJ[J R`LjL RTN[N R]PlP RX[[[ RTF[F[[ R_P_[ RdSjZ RjRfV RcPeU RWCWF RTFSR[RWVRX RdY^Z R`E`N_T',
	    'Po_ElE RTJ[J RbJjJ RbNjN RTN[N RaQlQ R_ZlZ RX[[[ RTF[F[[ R_E_Z RbGbN RbGjGkN RTFTR[RVWRY RcTkW RWCWF ReOcT RiSgVaX',
	    'Po^GkG RTJ[J R_KkK RTN[N R^OlO R^TlT RW[[[ RTFTR RTF[F[[ RfVj[ RaCbF R\\QSY ReGeSgX RWCWF RhCgF RcV`Z][ R[QRR ReNcW',
	    'Po_FlF RTJ[J R`MlM RTN[N RaQjQ RW[[[ Rb[e[ RWCVF[F[[ ReDe[ RaJjJjQ ReTjY RaScU RjSgV RVWRY RTFSR[RTX ReV_Y R_F^T][',
	    'Po^EkE RTJZJ RbMiM RTNZN R`VdV RgVkV R^ZlZ RV[Z[ RTFTR RTFZFZ[ R^E^[ R`P`V RbHbM R`PdPdV RgPgV RbHiHiM RgPkPkV R[QSY RWCVF R[QRS',
	    'Po]HlH RTJ[J R_KjK RTN[N R^QkQ R]TlT RW[[[ RTFTR RTF[F[[ R^M^Q R_E_K ReCeP R_EjEjK R^MkMkQ R[QSY RWCWF RcQ`WkZ RiUeY][ R[QRR',
	    'PoTJZJ RTNZN RV[Z[ Re[i[ RTFZFZ[ R]N]Q R]E]H R]NlNlQ R`HcK RhHlL RkEjH RbV^Z RTFSRZRSY RWCVF RcEcH ReEeH R^K\\L RfKdL RdPbX R`C_J RhCgJ R]SjSi[',
	    'Po^IcI RfIkI RTJ[J RTN[N R_PkP R_SkS R]WlW RW[[[ RTFTR RTF[F[[ R_L_S R^DcDcI ReLe[ RfDfI R_LkLkS RfDkDkI R[QSY RWCWF R^E^I R[QRR',
	    'Po\\FdF RTJYJ R[MlM RTNYN R]TcT R]XcX RU[Y[ RTFTR RTFYFY[ R]P]Y R^H^L R`C`G R]PcPcX RiDlG RhTb[ RYRRY Ri[k[ RjOgW RlWk[ RWCUF RYQRR RgCgPiZ RbHbL',
	    'Po]ElE RTJYJ RdLlL RTNYN RePkP ReUkU ReZlZ RU[Y[ RTFTR RTFYFY[ RaIa[ ReIe[ RiIiZ RYRRY RiGhM RVCUF RaG_P ReGcP RYQRR R]E]T[[',
	    'Po^IkI RTJ[J R^LkL RTN[N R]OlO R_VjV R][l[ RW[[[ RTFTR RTF[F[[ R^F^L R_R_V RbCbL RfCfL R_RjRjV R^FkFkL R[QSY RaWbY RhWgZ RWCWF R[QRR',
	    'Po]ElE R^HkH RUJZJ R_MiM RUNZN R^PkP R\\RlR RdUlU R^YbY Rg[i[ RUFUR R^T^Z R^TbTbY RdMdO RdCdH RiSi[ ReWgY R[QSY R]KlKkM RWCVFZFZ[W[ R[QRS',
	    'Po]FlF RTJYJ RdKkK RdMkM RTNYN RdPkP RdSlS RU[Y[ RTFTR RVCUFYFY[ RaIaS RdCdF ReIeS RhIhS RjWlZ RYRRY RcTgV RdVc[hZh[ RaG^N ReGbM R`W^[ RYQRR R]F]S[[',
	    'Po]HbH RTJZJ R]KbK ReIhJlK R\\NbN ReNkN RTNZN R^RjR R^UjU R^XjX RV[Z[ RWCUFZFZ[ R^P^W R`H`M RhHhN R^PjPjW R]E]GlElG RdCeE RTFSRZRSY RfYl[ RbY\\[',
	    'PoSFkF RUNiN RUSiS RRWlW RUIUS R_C_[ RUIiIiS',
	    'PoRG_G RSN_N RSR_R RRV_V Rc[k[ RSJSR RYCY[ RSJ_J_R RcDcZ RlUk[',
	    'PoRG^G RSN]N RSR]R RRV^V Ri[l[ RSJSR RXCX[ RSJ]J]R R_IiIi[ RlWl[ RcCcNbT`W][',
	    'PoVIiI RVPiP RVTiT RRWlW RVLVT R_F_[ RVLiLiT RSEkEkI RSESI',
	    'PoSFkF RUKiK RUOiO RRRlR RVZhZ RUHUO RVUV[ R_C_T RVUhUh[ RUHiHiO',
	    'Po_FkF RRG^G RSN]N RSR]R RRV^V Rg[l[ RSJSR RXCX[ RSJ]J]R RhFh[ RlVl[ RbFbQaW',
	    'PoRG\\G RSN\\N R]OgO RSS\\S RRV\\V RSJSS RWCW[ RSJ\\J\\S RbEb[ RlWl[ R^EiEiUjZ',
	    'PoRG^G R_KlK RSN]N RSR]R RRV^V RSJSR RXCX[ RSJ]J]R RhWl[ RcV][ RfRiY ReCeQaX ReJgW ReKcU',
	    'Po`ElE RRG^G RSN]N R_NlN RSR]R RRV_V RSJSR RXCX[ RSJ]J]R RfEf[',
	    'PoRF\\F RSN\\N RSS\\S RRV\\V Rh[l[ RSJSS RWCW[ RSJ\\J\\S R_EhEi[ RaLfQ RlWl[ R_E_Q^W',
	    'PoRG^G RSN]N RSR]R RRV^V Re[j[ RSJSR RXCX[ RSJ]J]R RcV^[ RePaY RaK`Q R`FlFlSj[ RfFfKdU',
	    'PoRG^G RSN^N RSS^S RRV^V R`YlY RSJSS RXCX[ RSJ^J^S R`J`Y RfDfY RlJl[',
	    'Po_ElE RRG^G RSN]N R`OkO RSS]S RRV^V R_ZlZ RSJSS RXCX[ RSJ]J]S RfEfZ',
	    'PoRG^G RhLlL RSN]N RSR]R RRV^V RSJSR RXCX[ RSJ]J]R RbDiDiL RaPcUgYl[ R_PjPhTdX^[ RbFaL',
	    'PoRG\\G R_GlG RSN\\N RSS\\S R_SkS RRV\\V Re[k[ RSJSS RWCW[ RSJ\\J\\S R_K_S ReCe[ RkKkV RlXk[',
	    'PoRG^G ReHkH RSN]N RSS]S RRV^V RSJSS RXCX[ RSJ]J]S ReCeM RaOdUhYl[ R_NjNgTcY][',
	    'PoRG]G R_KlK RSN\\N RSR\\R RRV]V RSJSR RXCX[ RSJ]J]R RdUgY RbW^[ RgRk[ RdQ`Y ReCeNiX ReHhV ReIcT',
	    'Po_ElE RRG]G RSN\\N RSR\\R RgSjS RRV]V Rc[k[ RSJSR RXCX[ RSJ\\J\\R RdJdZ RcJkJkR RlWk[ R_E_S^Z',
	    'PoRG]G RSN\\N RSR\\R RRV]V Ri[l[ RSJSR RXCX[ RSJ\\J\\R R_J_[ R_JlJl[ RfMjT ReMaU RaDfDfJhS ReGcR RfFgQ',
	    'PoRG\\G RSN\\N R_NlN RSS\\S RRV]V RSJSS RWCW[ RSJ\\J\\S R_F_[ RlWk[ ReY^Z RfEfOgY RjD_F',
	    'PoRG]G RSN\\N RSS\\S RRV]V Rd[k[ RSJSS RWCW[ RSJ\\J\\S RdFd[ RlWk[ RiD_G RkK_M RlQ^S',
	    'PoRG^G RSO^O RSR]R RRV^V RSJSR RXCX[ RSJ]J]R RaHkHjO RbC_N ReHePbW^[ RePgWl[',
	    'PoRF^F RSN\\N R_OlO RSS\\S RRV^V R`ZkZ RSJSS RXCX[ RSJ]J]S RfEfZ RkD`F',
	    'PoRG\\G RSN\\N RSS\\S RRV\\V RSJSS RWCW[ RSJ\\J\\S RcKfN ReElL RcG^L R_QjQe[ RdCbI',
	    'PoaFkF RRG^G R_NlN RSN^N RSR^R RRV_V RbZlZ R^ZgZ RSJSR RXCX[ RSJ^J]R RhSl[ RdN`Z',
	    'PoSFkF RUKiK RUNiN RRQlQ RUHUN R_C_T RUHiHiN RUS\\U RiSbU RaXl[ R\\XRZ',
	    'PoRG]G RSN\\N R^QlQ RSS\\S RRV]V Ra[e[ RSJSS RWCW[ RSJ\\J\\S ReEe[ R`HaN RkHhN RjD_F',
	    'PoRG]G RSN\\N RSS\\S RaTgT RRV]V RSJSS RXCX[ RSJ\\J\\S RaMaV RaMgMhT RbD^M Rj[f[ RaHlHlTj[',
	    'PoRF]F RSN\\N R^NjN RSS\\S RRV]V Rg[l[ RSJSS RWCW[ RSJ\\J\\S R^E^N RgNg[ R^EjEjN RlXl[ R`W\\[ RbN`X',
	    'PoRG]G R_NlN RSN\\N RSS\\S RRV]V RSJSS RXCX[ RSJ\\J\\S R_F_[ RdVfZ RlWk[ RdZ^Z RjD_F RfEfOhZ',
	    'PoRG^G RaLiL RSN]N RSR]R RRV^V RgXjX RSJSR RXCX[ RSJ]J]R RcPc[ R_PjPjX RfElK RdCcG^K',
	    'PoRF]F R_MjM RSN\\N RSS\\S RRV]V R`ZjZ RSJSS RWCW[ RSJ\\J\\S R`Q`[ R`QjQj[ RiHkN RdC`K',
	    'PoRG[G RSNZN RSRZR RRV[V RSJSR RWCW[ RSJZJZR RaD\\N RbK^T RaRcV\\V Rj[g[ RcIlIlTk[ RgCgMeX RbM\\N',
	    'PoRG]G RSN]N RSR]R RRV^V RSJSR RXCX[ RSJ]J]R ReClJ ReJaO RdE^K RhOdS`T RkSgX_[',
	    'PoVHeH RRKlK RVShS RVVhV RSXkX RVPVU R_L_[ RVPhPhU RcEfFiI R]HWNeMlO RZLRP R\\EVH',
	    'Po^ElE RRG\\G RaIkI RSN\\N RaOjO RSR\\R RaUkU RRV\\V R^YlY RSJSR RWCW[ RSJ\\J\\R R^E^[ RfIfU',
	    'PoRG\\G RSN\\N R]QlQ RSR\\R RRV]V RSJSR RWCW[ RSJ\\J\\R R_H_P R_HjHjP RfVk[ RcU^[ RdCdRhY ReQaX RdNcV',
	    'Po_ElE RRG\\G RSN\\N R^QlQ RSS\\S RRV]V RSJSS RWCW[ RSJ\\J\\S ReEe[ R`HaN RjHiN',
	    'PoSF\\F RSNkN RUSiS RUViV RRXlX RUPUV R_L_[ RUPiPiV RbCbKkJjK RjEbF R\\C\\JRK',
	    'PoRG\\G RSN[N RSR[R RRV\\V RSJSR RWCW[ RSJ[J[R RcGdY_Y RiDiKiNkZ R^E^Q]Y RiD^E RfThYaY',
	    'PoRG\\G R^JlJ RSN\\N RSS\\S RRV\\V R_ZkZ RSJSS RWCW[ RSJ\\J\\S R_Q_[ ReCeQ R_QkQk[',
	    'PoRF]F RSN]N R`RgR RSS]S RgUkU RRV^V Ra[k[ RSJSS RXCX[ RSJ]J]S RaKaZ R`KgKgR RaGlGlT RlWkZ RbC^L',
	    'PoRG]G R_IlI RSN\\N R`QkQ RSS\\S RRV]V R^ZlZ RSJSS RXCX[ RSJ\\J\\S ReIeZ RdDfF',
	    'PoRG\\G RbJlJ RSN\\N RSS\\S RRV\\V Re[i[ RSJSS RWCW[ RSJ\\J\\S RaGa[ RiCi[ RdNfT RbC]N',
	    'PoRG]G R_LkL RSN\\N RaQkQ RSS\\S RRV]V R`[l[ RSJSS RWCW[ RSJ\\J\\S R_GkGkL RlXl[ ReT`Y RdCfG R_G_P^X',
	    'PoRG\\G R]IkI RSN\\N RSR\\R RRV]V RSJSR RWCW[ RSJ\\J\\R ReUk[ RfDiE RfVa[ RbCbOfW RhPdX R`R][ RaIeU RiMfV RbI_W',
	    'PoRG^G RSN]N R`QlQ RSR]R RRV^V R`ZlZ RSJSR RXCX[ RSJ]J]R R`I`[ RfCfZ R`IlIl[',
	    'PoRG^G RSN]N R`QkQ RSR]R RRV^V RSJSR RXCX[ RSJ]J]R R`E`Q R`EkEkQ RiTl[ RbT_Z',
	    'Po`ElE RRG^G RSN]N RgNkN RSR]R RRV^V R`ZkZ RSJSR RXCX[ RSJ]J]R R`Q`[ R`QkQk[ RdK`O ReEcL RlFkN',
	    'PoRG^G RSN]N RSR]R R`SfS RRV^V Re[j[ RSJSR RXCX[ RSJ]J]R R`K`U R`KfKfS R_EkEj[',
	    'PoRG]G R`IkI RSN]N R^PkP RSR]R RRV^V RSJSR RXCX[ RSJ]J]R RgVl[ RcU^[ RfCeQiY RePaX RaD_M ReMfU',
	    'PoRG]G RSN]N RSR]R R`SjS RRV^V R^ZlZ RSJSR RXCX[ RSJ]J]R ReOeZ RaFbIfMlO R_EjEiHdM^O',
	    'PoRG]G R^IkI R^NdN RSN\\N RSR\\R RRV]V RiZk[ RSJSR RXCX[ RSJ\\J\\R RaNaY RhDkG RlWk[ RgCgQhZ ReX]Y',
	    'PoRG\\G R_IlI RSN\\N R^QlQ RSS\\S RRV\\V RSJSS RWCW[ RSJ\\J\\S RhIh[ RaDbH RiDfI RbIbRaW',
	    'Po^ElE RRG\\G RSN\\N RSR\\R RRV\\V RSJSR RWCW[ RSJ\\J\\R R_K_[ RcJcZ RgKgZ RdEcN R_KkKk[i[',
	    'PoRG]G R^JkJ RSN\\N RSS\\S R^SlS RRV]V RSJSS RWCW[ RSJ\\J\\S RbCbS RhChS RhVkY RaW][',
	    'PoTFjF RRJlJ RVRhR RVUhU RSXkX RVPVU R_K_[ RVPhPhU RdCdJgM RZCZJWMeMlO RXMRO',
	    'PoRG]G R_GlG RSN]N RSR]R RRV^V RSJSR RXCX[ RSJ]J]R ReCeG RdTjZ RhJkM RbK^N RbOeV RiOfUbY][',
	    'PoRF\\F R^NlN RSN[N RSS[S RRV\\V Rg[l[ RSJSS RWCW[ RSJ[J[S ReCeN RgNg[ RaW][ R_FaK RkEhK RlWl[ RbNaW',
	    'PoRG]G RSN]N RSR]R RRV^V R`ZjZ RSJSR RXCX[ RSJ]J]R RaQa[ R^RjRj[ RbGdLhPlQ RcC`J RbGjGhKcP^R',
	    'PoRG]G R^HlH RSN\\N R_OfO RSS\\S RRV]V RSJSS RWCW[ RSJ\\J\\S RgVl[ RcK_O RbS^T RiK`T RdCeF RdHbL RkQdX][',
	    'PoRG\\G R_NjN RSN\\N RSR\\R R_TjT RRV]V R^ZlZ RSJSR RWCW[ RSJ\\J\\R ReNeZ RfFlL R`EeDgH RcG^M RdDbI',
	    'PoRG]G RSN\\N R^OlO RSR\\R RRV]V Rg[i[ RSJSR RXCX[ RSJ\\J\\R ReQeX RdCdG RaGjGj[ RdIeM RaG`S_Z',
	    'PoSF_F RRJlJ RSM`M RTR_R RTU_U RRX`X RTPTU RYCYJ RYKY[ RTP_P_U ReXi[ RgDjG RfS_[ RjLeV RlVk[i[ RcCcKeW',
	    'Po_ElE RRG^G R^MkM RSN]N RSR]R R_SkS RRV^V R]ZlZ RSJSR RXCX[ RSJ]J]R ReOeZ RiIkO RdE`M',
	    'PoTGjG RRJkJ RVRhR RVUhU RSXkX RTDTG RVPVU R_K_[ RVPhPhU RjDjG RWMeLlO R_D_G[KRO',
	    'Po^ElE RRG\\G RSN\\N RSR\\R RbVhV RRV]V Rh[l[ RSJSR RWCW[ RSJ\\J\\R R^J^[ RbNbX ReEeU RhNhX R^JlJl[',
	    'PoRG]G RSN\\N RSS\\S RRV]V R`ZjZ RSJSS RWCW[ RSJ\\J\\S R`R`[ R`RjRj[ RfLlQ RfDkH RcL^Q RbF^J ReHgN RdHbN RbDbF',
	    'PoRG]G RSN\\N R_PlP RSS\\S R_UlU RRV]V Rh[k[ RSJSS RXCX[ RSJ\\J\\S R_K_[ ReCeK R_KlKl[ RkDjI R`EbI',
	    'Po^ElE RRG\\G RSN\\N RgQlQ RSR\\R R_VlV RRV]V R_YlY RSJSR RWCW[ RSJ\\J\\R R_J_[ RcEcO RhEhP R_JlJl[',
	    'PoRG\\G R`KjK RSN\\N R^PlP RSR\\R RRV]V Rg[l[ RSJSR RWCW[ RSJ\\J\\R R^G^J RgPgZ R^GlGlJ RlWl[ RaW][ ReCeG RcPaX',
	    'PoRG\\G R^JlJ RSN\\N R_NjN RSS\\S RRV\\V RaZkZ RSJSS RWCW[ RSJ\\J\\S RaQa[ R_EjEjN R`SkSk[ RdFaU',
	    'Po\\EkE RRG\\G R^KdK RSN[N R^QdQ RSR[R RRV\\V RgZlZ RSJSR RWCW[ RSJ[J[R R^E^W RdEd[ RhFhZ RlVlZ RdV\\W',
	    'PoRG]G RSN\\N R_RkR RSR\\R RRV]V Rf[l[ RSJSR RXCX[ RSJ\\J\\R R`I`R RfRf[ R^KkKkR RlXk[ R`H\\L RbC_K RcV`Y\\[ RbFhFeKcV',
	    'PoRG]G R_GlG RSN\\N R_PkP RSR\\R R_UkU RRV]V Rh[k[ RSJSR RXCX[ RSJ\\J\\R R_L_[ ReCe[ R_LkLk[ RhDkE',
	    'Po^ElE RRG\\G RSN\\N RSR\\R R^SkS RRV\\V R\\ZlZ RSJSR RWCW[ RSJ\\J\\R ReSeZ RiMkQ R^K`Q RcKeQ RjGhL R`G^L ReGcL',
	    'PoRG\\G RaIjI RaMjM RSN\\N RSR\\R RRV\\V RbWiW Rh[l[ RSJSR RWCW[ RSJ\\J\\R RbQbX ReFeO RbQiQiW R_ElEl[ R_E_S]Z',
	    'PoRG^G R`KdK RgKkK RSN]N R`QkQ RSR]R RRV^V Rg[l[ RSJSR RXCX[ RSJ]J]R R`F`Q RhQh[ RfEkEkQ RlWl[ RbW^[ ReD`E RcQaY',
	    'Po_FjF RRG\\G R^JkJ RSN\\N RSR\\R RRV]V RSJSR RXCX[ RSJ\\J\\R RdCdJ RgJfOkNkO RbP^U R`SeXl[ RbJ`O]O RaSiSdX][',
	    'Po\\EkE RRG\\G R^KcK RSN[N R^QcQ RSR[R RRV\\V RSJSR RWCW[ RSJ[J[R R^E^W RcEc[ RgRk[ RjQfZ RfLiX RkLhX RfJhW RcIkIiW RcW\\X',
	    'PoRG\\G RcGlG RaHiH R^HfH RSN\\N RSS\\S RbSjS R_TfT RRV]V RSJSS RWCW[ RSJ\\J\\S RlXk[ RdJhN RbReY RfPjQ RiUeWgZj[ RfDjE RgW^[ RbOdW RbCeK RiJcL^N RkKkNiN',
	    'Po]FlF RRG\\G RSN[N RSR[R RRV\\V RSJSR RWCW[ RSJ[J[R R^J^[ ReFe[ RbNcV RhNjU RhNfV RbN`V R^JlJl[h[ RhNfM RbN`M',
	    'PoRG\\G RSN\\N RSR\\R R_UkU RRV]V R_ZkZ RSJSR RWCW[ RSJ\\J\\R R_P_[ RePeZ R_PkPk[ RcIfL RhIkM RjEhI ReDcI R^H_KaN R`D^I',
	    'Po^IkI RRM\\M R_PkP R_SkS R]WlW RWDXMYW R^E^H R_M_S ReGe[ R_MkMkS R^EkEkH R[EZK R]VXW RSETK RUMURSZ',
	    'PoRG\\G R`LkL RSN\\N RbPjP RSR\\R RRV]V RbWiW Ri[l[ RSJSR RWCW[ RSJ\\J\\R R_E_[ RbPbV ReLeP R_ElEl[ RbGcK RhGhK',
	    'PoRG\\G RSN[N RSR[R RRV\\V RSJSR RWCW[ RSJ[J[R RgVk[ R`UcX R^HaIcL RiIlL RiJeN R]TaV\\[ RjVe[ RaJ]N ReRhY ReHjJ ReEkEhL ReQkQhX R^EcE`L R]QcQ`Y',
	    'PoRG\\G R_IjI R_NjN RSN[N R]RkR RSR[R RRV\\V RSJSR RWCW[ RSJ[J[R R_E_N RdEd[ R_EjEjN RfTlZ ReRgV RcT\\Z',
	    'PoRG\\G RfLjL RSN\\N RSR\\R R_TdT RgTlT RRV]V R_ZlZ RSJSR RWCW[ RSJ\\J\\R R_O_[ RfOlOl[ RcC`K RdN_P RcFlFkL',
	    'Po_FlF RRG\\G RSN\\N RSS\\S RRV\\V R`WfW Rf[j[ RSJSS RWCW[ RSJ\\J\\S R`Q`X R`QfQfV R^NkNj[ RdHkK ReHbK_L ReCdJ',
	    'PoRG\\G RSN\\N RSR\\R RRV\\V R`[d[ Ri[l[ RSJSR RWCW[ RSJ\\J\\R R_DdDdZ RgDlDl[ R`PbR R`IbK RhPjR RhIjK R_D^R]Z RgDgTe[',
	    'Po_FlF RRG]G R_IkI R^LlL RSN\\N RSS\\S R`SkS RRV]V R`WkW Rg[k[ RSJSS RXCX[ RSJ\\J\\S R`O`[ ReCeL R`OkOk[',
	    'PoRG]G R_IkI R_MkM RSN\\N RSR\\R R_SdS RRV]V Rf[l[ RSJSR RWCW[ RSJ\\J\\R R_D_M R_O_Z RgOg[ R_DkDkM RlXl[ RkRfT RdY]Z',
	    'PoSE]E R`EkE RSH]H R`HlH RUNiN RVSiS RVViV RRXlX RVQVV R_L_[ RVQiQiV ReHaL RXCXITM RWJ]L ReHhLlM ReDdJ',
	    'PoRG\\G R]IdI ReIlI RSN\\N R]QcQ ReQlQ RSR\\R RRV\\V RSJSR RWCW[ RSJ\\J\\R RhHh[ RjCgI RbC`IaR_X',
	    'PoRG\\G RSN\\N R`PjP RSR\\R RRV]V R`ZkZ RSJSR RWCW[ RSJ\\J\\R R^F^J R`J`[ ReCeG R`JjJjP R`TkTk[ R^FlFlJ',
	    'PobDkD RbGkG RbJlJ RSNkN RVSiS RVViV RRXlX RVQVV R_L_[ RbCbL RVQiQiV R\\C\\JYLUM RTD\\D RTG\\G RUJ\\J',
	    'PoRG\\G R`KiK RSN\\N RSR\\R R^TkT RRV\\V RSJSR RWCW[ RSJ\\J\\R R^O^[ RcOc[ RgOg[ ReCkJ RdC^J R^OkOk[h[',
	    'PoRG\\G R^HbH RgHlH R^NbN RgNkN RSN\\N RSR\\R RgTlT RRV\\V RSJSR RWCW[ RSJ\\J\\R RgCg[ RaW][ RbCbT]T RbOaX',
	    'PoRG\\G R^GlG RSN\\N R`QjQ RSR\\R RRV]V Rb[e[ RSJSR RWCW[ RSJ\\J\\R R`K`Q ReCeG ReQe[ R`KjKjQ RjVkY R`T^Y',
	    'Po^ElE RRG\\G RSN[N RSR[R R^TlT RRV\\V RSJSR RWCW[ RSJ[J[R R_I_P RcHcP RgIgP RdSfWhZl[ RdEcK RcVaY][ RdQcX R_IkIkPiP',
	    'PoRG\\G RSN[N RSR[R R]SlS RRV\\V RSJSR RWCW[ RSJ\\J\\R RbOePd[ RdRkY RbL^N RdI`M R^IkIiM RdR]Y R_EjEdG RbFfIeNdQ',
	    'PoRG^G R`IjI RSN]N RaPjP RSR]R RaTjT RRV^V R`XjX R^YgY RSJSR RXCX[ RSJ]J]R R`D`I RaLaX R_LkLj[ R`DjDjI',
	    'PoRG\\G R`HjH R`LjL RSN[N R^OlO RSS[S RRV\\V Rg[j[ RSJSS RWCW[ RSJ\\J\\S R`D`L R`DjDjL RbV^Z RhRdZ RaO_T RcRaX R`SkRj[',
	    'PoRG[G R]HlH R_LeL RSNZN RSSZS R`UdU RRV[V RSJSS RWCW[ RSJZJZS R`O`W R`OdOdU RhDkF RgUb[ RjNeX RlWj[k[ RgCgMgOhZ R]H]R\\Z',
	    'PoRG\\G R_JkJ R]MlM RSN\\N RSS\\S RRV\\V Ri[l[ RSJSS RWCW[ RSJ\\J\\S R_Q_[ R_E_J RcQc[ RdMdQ ReCeJ RgQg[ RkEkJ R_QlQl[',
	    'Po^GkG RRG\\G R_JkJ R]NlN RSN\\N RaQiQ RSR\\R R_UkU RRV\\V RSJSR RWCW[ RSJ\\J\\R RgMlR RbN]R RfJiP RdTfWhZk[ RdI`P RbY][ ReQcX RdCbN',
	    'PoRG\\G R_IlI RSN\\N RaRkR RSR\\R RaVkV RRV\\V RaZkZ RSJSR RWCW[ RSJ\\J\\R RaNa[ RfEfN RaNkNk[ R_E^R]Z RjD`E',
	    'PoRG]G R`HkH R`LkL RSN\\N RSR\\R RRV]V R`XiX RSJSR RXCX[ RSJ\\J\\R R`D`L R`Q`Y R`DkDkL ReShV RaO]S ReSbV RbL`QlPlVk[g[',
	    'PoRG\\G RSN\\N RfRkR RSR\\R R_UkU RRV]V R_YkY RSJSR RXCX[ RSJ\\J\\R R_N_[ R`JhJgR R_NkNk[ RcJcQ`S R`F^J RbEhEjI',
	    'PoRG\\G R`JgJ R^MiM RSN\\N R`OgO R`RgR RSR\\R RRV]V R`XgX Rg[j[ RSJSR RWCW[ RSJ\\J\\R R`T`Y R`TgTgX RaC^J R_HlHlTk[',
	    'PoRG\\G RaJhJ RSN[N R^RcR RSR[R R^UcU RRV\\V Rh[k[ RSJSR RWCW[ RSJ\\J\\R R^N^[ RgNgW RkMk[ ReCkJ RdC]J R^NcNc[a[',
	    'PoaGkG RRG]G R`MjM RSN\\N R`PjP RSR\\R RRV^V RSJSR RXCX[ RSJ\\J\\R R`I`P R`IjIjP RbC`H^I RdP^V RaTeXl[ RaTjTfXbZ\\[',
	    'PoRG\\G R]IlI RSN\\N RSR\\R R_UkU RRV\\V R_ZkZ RSJSR RWCW[ RSJ\\J\\R R_P_[ RePeZ R_PkPk[ R_HbN RdHfN RiHkN RkDiI R`C_I ReDdI',
	    'Po_ElE RRG]G R`MkM RSN\\N RSR\\R R_UlU RRV]V R_ZlZ RSJSR RXCX[ RSJ]J]R R_Q_[ R`H`M RfQfZ R`HkHkM R_QlQl[',
	    'PoRG\\G R_IjI R_NjN RSN\\N RSS\\S RRV\\V R\\[l[ RSJSS RWCW[ RSJ\\J\\S R^R^Z R_E_N RbRbZ RfRfZ R_EjEjN R^RjRjZ',
	    'PoRF\\F RcHjH R^HfH R]LlL RSN[N RSS[S R^UjU RRV\\V R^ZjZ RSJSS RWCW[ RSJ[J[S R_P_[ RdQdZ R]QjQj[ RgEkI RcL^Q RcC^H ReLhOlP RcIaN',
	    'PoRG\\G R_IkI R`NkN RSN\\N RSR\\R R_RlR RRV\\V RSJSR RWCW[ RSJ\\J\\R RbRbZ RhKhR R_EkEkI RgWk[ RkUgW ReRhY RcKcQbU RfY_Z R_E^R]Y',
	    'PoZFdF RUJYJ ReJjJ R[KcK R[OcO RUOYO ReOiO RZQdQ RRUlU RUEUU R[H[O R_C_U R[HcHcO ReEjEjU RcWl[ R[WS[ RZDTE',
	    'PoRG\\G R_NjN RSN\\N RSR\\R RRV\\V R]ZlZ RSJSR RWCW[ RSJ\\J\\R R_Q_Z R_E_N RcQcZ RgQgZ R_EjEjN R_QkQkZ RdIhL ReFcL',
	    'PoRG\\G RSN\\N RSR\\R R_TcT RfTkT RRV\\V R_ZkZ RSJSR RWCW[ RSJ\\J\\R R_O_[ RfOkOk[ RiFlL R`E^L RdN_P RdGeK RiD`E',
	    'PoRE_E RTH^H RUL\\L RiMlM RSO^O RTT]T RTV]V RRX_X RTQTV RYCYH RYNY[ RTQ]Q]V RSJSK_J^M RcDcK RcDiDjL RlJlL RcPeViYl[ RaPjPhTdY_[',
	    'Po^FkF RRG\\G R_JkJ R]MlM RSN\\N RSR\\R R^TkT RRV]V RSJSR RWCW[ RSJ\\J\\R RdQd[ RdSiY RgMlR RfJiP RaN]R RdT^Y RcI`Q RdEaO RgP`Q',
	    'PoRG]G R`JkJ R`NkN RSN\\N R^QlQ RSR\\R RRV]V R`ZkZ RSJSR RXCX[ RSJ\\J\\R R_F_I R`T`[ ReCeG ReHeQ R`TkTk[ R_FlFlI',
	    'Po_FkF RRG\\G R^JlJ RSN\\N R_QjQ RSR\\R RRV\\V RSJSR RWCW[ RSJ\\J\\R R_M_Q RbTbZ ReCeI R_MjMjQ RgVk[ ReQhY RdS^U RlTgV RfY^Z',
	    'Po^FlF RRG\\G RSN[N R^PkP RSR[R R`UdU RRV\\V Rf[l[ RSJSR RWCW[ RSJ[J[R R`Q`[ RbHbP ReCeF RgQg[ RgHgP R^KkKlP RlXl[ RkSfV ReZ^Z R^F^S\\Z',
	    'PoRG\\G RSN[N RSR[R RRV\\V RSJSR RWCW[ RSJ[J[R ReDkDkL RhEeH RiHfK R`E]H RaH]L ReMhPlR R]DcDcL`Q]R RdR_T RfT_W RiWeY^[',
	    'PoRG\\G R^NjN RSN[N R^QjQ RSS[S R^TlT RRV\\V RSJSS RWCW[ RSJ[J[S R^J^Q RdJd[ R^JjJkQ RdTjY R^F`I RcFeH RhFkI RjChF R`C^F ReCcF RdT`X\\Z',
	    'Po^ElE RRG]G R_KkK RSN\\N R^RkR RSR\\R R^UlU RRV]V Rd[h[ RSJSR RXCX[ RSJ\\J\\R ReCeQ RhSh[ R_HkHkN R`VcY R_H_NjOlR',
	    'Po^FdF ReFlF RRG\\G R^IdI RfIlI ReMlM RSN\\N RSR\\R R^VlV RRV\\V R_ZjZ RSJSR RWCW[ RSJ\\J\\R RaCaO RhChO R_QjQj[ RdM]M',
	    'PoaFkF RRG[G RbMkM RSNZN RbQkQ RSRZR RaUlU RRV[V Rc[l[ RSJSR RVCV[ RSJZJZR RbJbQ RfCfY RbJkJkQ R\\E_H R_M_X\\Z R_Xd[ R_M\\M',
	    'Po_EkE RRG]G R`KcK RgKjK RSN\\N R_NcN RgNjN RSR\\R R_VlV RRV]V R_ZjZ RSJSR RXCX[ RSJ\\J\\R R^H^K ReEeO R`RjRj[ R^HlHlK',
	    'PoRGZG R\\GdG R\\LcL RSNZN RSRZR R]RcR RRV[V R]VcV R`[c[ RSJSR RWCW[ RSJZJZR R]O][ R]OcOc[ RbIcM RgRk[ RiQeZ R`E^L RfJiW RgCdO RfIkIiV',
	    'Po_EgE RRG\\G R^HlH RSN\\N RaOiO RSR\\R RRV]V RbXhX Rh[k[ RSJSR RXCX[ RSJ\\J\\R R^Q^[ RaJaO RbTbY RbThThX R`KiJiO R^QkQk[ RgHkL RcG^L RdDbI RiDcD',
	    'PoRG\\G R^HkH RbKjK RaMkM RSN[N RSR[R RRV\\V RfWlW R`[b[ Rg[i[ RSJSR RWCW[ RSJ[J[R RbUb[ RiUi[ R^DkDkH RfSlSiU RfLfQcP R`SeScV_W R^D^R\\Y',
	    'PoRG\\G R^IlI R_MkM RSN\\N R_QkQ RSS\\S R^TlT RRV]V RSJSS RWCW[ RSJ\\J\\S RgCgI ReMeVj[ R_D`G ReSgX RcCbIaL RjDiG RhJgM RdVaY^[ RePdW',
	    'Po]GlG RRG\\G RSN[N R_PiP RSR[R R_SiS RRV\\V Ra[d[ RSJSR RWCW[ RSJ[J[R R`K`S RdSd[ R^MjLiS RhVkY R^I`K RfHlN RjHhK R`V]Y RbI]N RdC`K',
	    'Po^FkF RRG\\G R^JlJ RSN[N R_PkP RSR[R R_TkT RRV\\V R_WkW RSJSR RWCW[ RSJ[J[R R_M_W RaHaL ReCeH RhHhL R_MkMkW RgXl[ RbX\\[',
	    'PoRG\\G R]JkJ RSN[N RSR[R R^VjV RRV\\V R^ZjZ RSJSR RWCW[ RSJ[J[R R^Q^[ R^EeEeP RdQdZ R^QjQj[ RdJjO RdJ^P RiFgI R_FaI RjDbD',
	    'PoRG\\G R]HlH RSN[N ReQlQ RSR[R RRV\\V RcWlW RSJSR RWCW[ RSJ[J[R RdCdN ReQeV RiNi[ R`DaG RjDgG R^TbV][ RdG_M RdGhKlM R`N]U R_QcQ`Y',
	    'Po^FlF RRG\\G R_LjL RSN\\N R_OjO RSR\\R RRV\\V R_XiX RSJSR RWCW[ RSJ\\J\\R R_T_Z R_I_O RbCbG RgCgG R_IjIjO R_RlRlY RdTgW RaP]T RdSaW Rj[g[',
	    'Po^FkF RRG[G RSNZN R]OlO RSRZR RRV\\V RbXgX R^ZkZ RSJSR RWCW[ RSJZJ[R R^R^[ RbTbX RdCdN RbTgTgX R^RkRk[ R`JbL RiJkM RiHgL R`H^M',
	    'Po^GlG RRG\\G R`IdI RSN[N R`QdQ RSR[R RRV\\V Ra[h[ RSJSR RWCW[ RSJ[J[R R`L`P RbUb[ R`LdLdP RhXh[ RkQlS RjVkZ RbTfU RhDkE RjJfR R^V]Z RfDhQ R^G^N]S',
	    'PoRG\\G R^IkI R]LlL RSN[N RSR[R R_SjS RRV\\V RSJSR RWCW[ RSJ[J[R R^D^I RbUbZ RbDbI RgDgI R_NjNjS R^DkDkI RfVj[ RkUgW ReSgX RcT]W R_N_S RfY^Z',
	    'Po]FkF RRG[G R^IjI R\\MlM RSN[N RSR[R R\\SlS RRV[V RSJSR RWCW[ RSJ[J[R RaOa[ RdFdM RlYl[ RgXj[ RiOkP R`CaF RhCgF RjUgYcZ RcO]O R][a[][ RfNgY RdV\\W',
	    'PoRE^E RhIlI RSK]K RRL_L RSO]O RTQkQ RVVhV RRZlZ RSGSJ RXCXO RSG]G]J R_P_[ RbDhDiH RaJdMjO RbDaI RaJjJfN_O R_WbX_X RbXeX RVSVWZX R[X_X[XYX RVShShWeXcX R\\IXITH',
	    'PoRGZG RfIlI R\\JdJ RSNZN R\\OdO RSRZR R\\TdT RRV[V Rg[j[ RSJSR RVCV[ RSJZJZR R\\E\\H R]K]N R_D_J R`O`Y RaDaJ RjDj[ RdEdH RbKaO RgOgU RdX[Y',
	    'Po_ElE RRG\\G R_McM RgMjM RSN\\N R^PlP RSR\\R RRV]V Ri[l[ RSJSR RWCW[ RSJ\\J\\R R_T_[ RdSd[ ReEeN RgTg[ R_TlTl[ R^H^IlHlJ RdPcW R`JcJ`J RjJgJ',
	    'Po]EbE RUGZG RdGiG RVJZJ RdJiJ RRLlL RYOeO RXTfT RXVfV RTXkX RVEVL RXQXV R]I]L R_N_[ R]C]HaHaL RXQgQgV RdDiDiL ReLhPlR RZLVPRR RZDUE',
	    'Po^DdD RRG\\G RfHlH R^LdL RSN[N RfNlN R^OeO RSR[R RRV\\V R\\[l[ RSJSR RWCW[ RSJ[J[R R^D^Q R_S_Z RcScZ R^HdHdL RfSfZ R_SjSjZ RaLaP RaDaH RgDfL',
	    'PoTEjE RVKhK RRMlM RSP]P R`PkP RST\\T RaTkT RSW\\W RaWkW RRY^Y R_YlY RSRSV RVGVK RXOX[ RSR\\R\\V R_C_N RaRaV RfOf[ RaRkRkV ReIhI RYIVI R[I_I[I RVGhGhJdI RbI_IcI',
	    'PoRG[G R^IjI R^MjM RSNZN RSRZR R\\UcU ReUlU RRV[V R\\ZcZ ReZlZ RSJSR RWCW[ RSJZJZR R\\P\\[ R^E^M R`P`Z R\\PcPc[ RdEdM RePe[ RiPiZ R^EjEjM RePlPl[',
	    'Po[EcE R[JbJ RdLlL R[MbM R[PcP RVZhZ RVTV[ RVLVR R[H[M R_C_R R[HbHbM RhLhR RVThTh[ RRLYKZL RdFgI RiGeL ReNdQ RVCSGWHSL RgDeG RYNYQ RjIkMkQ RSNRR',
	    'PoRG[G RSN[N RbPjP RSR[R R\\SlS RRV\\V RSJSR RWCW[ RSJ[J[R R\\H_K RbFbP RdPd[ RdDbFfFfO RdSiXlY ReFiJ RkLkP RkHgO R^D^I RiDhI RdS_X[Y R`H\\P`PcO RbKfKbK',
	    'Po]ElE RRG[G RSN[N RSR[R ReUkU RRV[V R^ZlZ RSJSR RWCW[ RSJ[J[R RaTaZ RbGbQ ReReZ RiGiQ R_JcJdN RfJjJkP RhIfO RbJ_P RdG_G RkGeG R]E]S[[',
	    'PocEjE RRG\\G R_JhJ RcLkL RSN[N R`QjQ RSR[R R`SjS RRV\\V R^ZlZ RSJSR RWCW[ RSJ[J[R R^G_V_Z R`N`S RdCdL RcUcZ ReNeS RgUgZ R`NjNjS R_UkUkZ R^GkGkJ R^P\\Z',
	    'Po\\ElE RRG[G R]KcK RfKlK RSNZN R]NcN RfNlN R`RhR RSRZR R`ThT RRV[V R`WhW R`YiY Ri[l[ RSJSR RWCW[ RSJZJZR R]I][ RaQaZ RaCaG R]IbIbN RePeY RfIfN RhChG RfIlIl[',
	    'PoRGlG RUPjP RRUlU R`K`[ R[CWM',
	    'PoRG_G RSO_O Rc[k[ RZKZ[ RcDcZ RWDTO RlUkZ R]TRV R_TUU',
	    'PoRG]G RSO]O Ri[l[ RYKY[ R_JiJiZ RlWlZ RbS^[ RWDSO R]TRU RcDcNaW',
	    'Po`ElE RRG]G R_OlO RSO]O RYKY[ RfEf[ RWDSO R]TRU',
	    'PoRG^G R^KlK RSO^O RYKY[ RhWl[ RbU][ RfRiZ RWDTO ReCeMgU ReKcU R^TTV',
	    'PoRG^G RSO]O Re[j[ RYKY[ RcV^[ RePaY RWDSO RaJ`Q R^TRU R_FlFlTj[ RfFfLdU',
	    'PoRG\\G R^GkG R]LlL RSO\\O RXKX[ ReXh[ RbVfX RaQjQhTeX RWCSO ReCaQ R]TRU',
	    'Po_ElE RRG\\G RSP\\P RgSjS Rc[k[ RXKX[ RcJcZ RcJjJjS RlVk[ RWDSO R\\TRU R_E_S]Z',
	    'PoRG[G RSP[P Ra[j[ RXKX[ RaMaZ ReFlM RdDfG RkWj[ RcG\\M RdCaI RiO`S RWDSO R\\URV',
	    'PoRG^G RSO^O RYKY[ RhWl[ RcV^[ RfQiY RdQaX RXESO ReNhW RbHlHkN RcC`M ReLdU R\\TRV',
	    'PoSFkF RVJiJ RRNlN RSR]R R_RjR R`H`P R\\CVJ RbTfXl[ RTT[X R\\SWYR[ RjSeX^[',
	    'PoRG]G R^JlJ RSO]O R`ZkZ RYKY[ R`Q`[ ReCeQ R`QkQk[ RWDSO R]TRU',
	    'PoRG\\G RSO\\O R_SeS Re[i[ RYKY[ R_K_U R_KeKeS R]EkEj[ RWCSO R\\TRU',
	    'PoeFlF RRG]G RSO\\O R`QkQ RYKY[ ReCeK R`KkKkS RWDSO R\\TRU R`K`S^Z',
	    'PoRG\\G RSO\\O R_QlQ R_ZlZ RYKY[ R_I_[ RfCfZ R_IlIl[ RWCSO R\\TRU',
	    'PoRG\\G RSO\\O R_PkP RXKX[ R_E_P R_EkEkP RiTl[ RaT^[ RWCTO R\\TRU',
	    'PoRG]G R`IkI RSO\\O R^PlP RYKY[ RgWk[ RcU^[ ReCeQhY RePaX RWDSO RaE^M ReNfW R\\TRU ReLdU',
	    'PoRG\\G RSO\\O R^QlQ Ra[e[ RYKY[ ReEe[ R_HaN RkHhN RWDSO R\\TRU RjD^F',
	    'PoRG\\G RSO\\O RXKX[ RdDjK RcE\\L RdL^O RgOeQ^T RjSfW^[ RWCSO R\\TRU',
	    'Po_EfE RRG[G R^NlN RSP[P Ra[d[ RXKX[ ReHe[ RiRlY R`R]Y RWDSO R\\TRU R_F^N RjDaE',
	    'PoRG\\G RfNkN RSP\\P R`ZlZ RYKY[ R`Q`[ R`QlQl[ RcJ_O RWDSO RdEbL R\\TRU R_ElEkN',
	    'PoRG\\G RSP\\P R_QjQ R][l[ RXKX[ ReQeZ ReKlN R_EjEdL^N RWDSO R\\TRU',
	    'PoRG\\G R]IlI R^NdN RSO\\O RYKY[ RaNaY RhDjG RlWk[ RWDSO R\\TRU ReX\\Y RgCgQhZ',
	    'PoTF`F RRJlJ RRNaN RTS`S RZCZJ R[P[[ RgDjG RfU`[ RgZj[ RjLeW RXKTS RlVk[ RdCdLeW R[WTX R^WXW RaW[W',
	    'Po^ElE RRG\\G R_MjM RSP\\P R_SkS R]ZlZ RXKX[ ReOeZ RbI^M RiIkN RcEaJ RWCTO R\\TRV',
	    'PoRG\\G R]JlJ RSO\\O RXKX[ RhQh[ RfKlQ RbL]Q RdGaO R_Z][ RWDSO R\\TRU RaQaX RiD`E',
	    'PoRG\\G RSO[O R]OlO Rf[i[ RXKX[ R`GjGj[ RdRfV RdIeM ReCcG RVDSO R[TRU R`G`R^Y',
	    'PoRG\\G R_MjM RSP\\P R^SjS R\\[l[ RYKY[ RdMdZ RdDlL RdCcG\\L RWDSO R\\TRU',
	    'PoaGjG RRG\\G RSP\\P R_ZjZ RXKX[ R_R_[ R_RjRj[ RaG]K RaGbJgNlQ RbC`I RjGdN]Q RWDSO R\\TRU',
	    'PoRG[G R^GlG RSO[O RXKX[ RhJlN RdCeF RaK^O RaOdUiYl[ RhNfUaY][ RWDSO R[TRU',
	    'Po[ElE RRG[G R]KcK RRO[O R]QcQ RgZlZ RXKX[ R]E]W RcEc[ RhGhZ R[TRV RlVlZ RUCSO RdV\\W',
	    'PoRG[G R^GlG RSO\\O R_PkP R_UkU RYKY[ R_K_[ ReCe[ RgDkE RWDSO R_KkKk[h[ R\\TRU',
	    'Po\\ElE RRG[G RRO[O Rg[k[ RXKX[ R]K][ R]KkKk[ RbEbOcS RgEgOjU RVDSO R[TRU RbJaT RfKeU',
	    'PoSF]F R`FkF RSH]H R`IkI RTOjO RVTjT RRWlW R`Q`[ ReIaM RXCXITM R\\NWT ReHhLlM RXJ]L ReDeJ',
	    'PoaEjE RTH\\H RaHjH RSK\\K RaKkK RSPkP RUTiT RRXlX RTE]D]M R`Q`[ RaCaM R[NUT',
	    'Po_KjK RRM\\M R_RkR R^VlV RWDXMYX ReMe[ R^ElElI R\\UYX RSFTK R[FZK RcH_Q R^E^I RUMURSZ',
	    'PoRG\\G R^IjI R^MjM RSO\\O R_SdS Rf[l[ RYJY[ R^D^M R_O_[ RfOf[ R^DjDjM RlXl[ RkRfT RWCSO RdZ]Z R\\TRU',
	    'PoRG\\G R`LjL RSO\\O R`PjP RbViV Rh[l[ RXKX[ R^E^[ RbPbV R^ElEl[ RdMeP RbFcJ RhGfK RWDSO R[TRU',
	    'Po^GlG RRG\\G RSP\\P R_PjP R`[d[ RYKY[ R_K_P RdPdZ R_KjKjP RiSlY RdCeG R_T]Y RWDSO R\\TRU',
	    'PoRGZG RSOZO RWKW[ RfVk[ R_VbY RhJkM RjIdN RaU[Z RaJ\\N RdQhX R\\HaK R\\UaW RdHiJ RdEkEhK R\\EbE_L R[QbQ_W RdQkQiVfYb[ RVDSO RZTRU',
	    'PoRG\\G RSO\\O R^UlU R^ZlZ RXKX[ R^P^[ RePeZ R^PlPl[ RcIeL RhIlL RdEcI R_I`M R`D^H RjEhI RWCSO R\\TRU',
	    'Po^FkF RRG\\G R_JjJ R]MlM RSO[O RaQgQ R_UkU RXKX[ RhOlS RaO]S ReWkZ RcI`Q RcVaY][ RWDSO RdEaO RdQcX R[TRV',
	    'Po]ElE RRG[G R_MjM RSO[O R^UkU R^ZkZ RXKX[ R^P^[ R_H_M RdPdZ R_HjHjM R^PkPk[ RWDSO R[TRU',
	    'PoRG\\G R_IjI RSO\\O R`PjP R`TjT R`XjX R^YgY RXKX[ R_D_I R`L`X R]LkLj[ R_DjDjI RWDSO R\\TRU',
	    'PoRG\\G R_IjI R_NjN RSP\\P R\\[l[ RXKX[ R^R^Z R_E_N RbRbZ RfRfZ R_EjEjN R^RjRjZ RWDSO R\\TRU',
	    'PoRGZG R_JhJ RROZO R]QcQ R]UcU Ra[c[ RWKW[ R]M][ R]McMc[ RgNgW RdDgHlJ RdC_I[J RZTRU RjMj[g[ RVCSO',
	    'Po[FcF R[KcK RROYO ReOlO RZOcO RdRlR RVZhZ RVTV[ R_I_R RVThTh[ RlHeO RYHRN RVCTI RiCfI R]C[K RYQRR RWJRJ RjJdJ',
	    'Po^FjF RRG[G R]JlJ RSO\\O R_QjQ RXKX[ R_L_Q R`T`[ RdCdI R_LjLjQ RfUkZ RdQgX RcS\\V RkTfV RdY_Z RWCSO R[TRU',
	    'PoRG\\G R`JjJ R`NjN RSO\\O R_QlQ R`ZjZ RXKX[ R^G^J R`T`[ ReHeT R`TjTj[ R^FlFlI RdCeE RWDSO R\\TRU',
	    'PoRG[G R^IkI R_MkM RSO[O R^SlS RXKX[ RaRa[ RbJbR RhJhR R^DkDkI RfVkZ RkUgV ReRhX RWDSO RfY`Z R[TRU R^D^Q\\X',
	    'Po]FlF RRG[G RRPZP R]OkO R_TdT Rf[l[ RXKX[ R_Q_[ RbFbO RfQfZ RfFfO R]JkJkO RlXl[ RdCeF RlSfU RdZ_Z R[TRU RVDSO R]F]S[[',
	    'Po[GcG RRGZG RROZO R\\RbR R\\VbV R_[b[ RXKX[ R\\N\\[ R\\NbNbZ RfQjZ RiReZ R^C_F\\L ReKhW RgDdN RZTRU RUDSO RaIbL\\L RfIkIhV',
	    'PoRG[G R]IlI RROZO RdQlQ RdWlW RXKX[ RdCdN ReSdW RiOi[ R^DaF RjDgG R^U`V\\[ ReIhKlM R^O\\U R^RbR_X RdHaL\\M RUDSO R[TRU',
	    'PoTGjG RRNlN RSTkT R_N_[ RYIZM ReHcN R_C_G',
	    'PoRFlF RVLiL RTOjO RRTlT RSWkW RVIVL R_C_H R_L_O R_T_[ RVIiIiL RYPZT RePdS',
	    'Po`GkG RTM^M RRM[M R_NlN R`TkT RTZ]Z RTQT[ RTQ]Q][ ReNe[ R\\I^M RbIbM RiIhM ReCfG RWCTM',
	    'Po^GkG RRK]K R^NlN R^TlT RSY\\Y RSRS[ RXEXR RSR\\R\\Z ReCeG ReNe[ RaHaM RiHgM R\\DRF',
	    'Po_GkG RUL]L R^NlN R_TkT RUY]Y RUEURU[ RUE]E]K RUQ]Q]Y ReCeG ReNe[ RaIbM RhIgM RTNSX',
	    'PoVHhH RVKhK RVNhN RTQjQ RRUlU RSXkX RVEVN R_N_Q R_U_[ RVEhEhN RYRZU ReRdU R_C]E',
	    'Po_GkG RRG\\G RRN\\N R_NlN R_TkT RTIUM RWCWG ReCeG ReNe[ RbIbM RhHgM RZIYM RWNWTVY R\\SRT',
	    'PoSG^G R`GkG R_NlN RSQ]Q R`TlT RSKSP RXCX[ RSK]K]P ReCeG ReNe[ RWQ]W RVTRX RbIcM RiIhM RXOUV',
	    'Po^GkG RRG\\G RRN]N R_QkQ RTIUM R_K_Q RdCd[ R_KkKkQ RZIZM RWCXG R\\RRT RWNWTUZ ReQbT\\Y ReQgTmY',
	    'Po`GkG R_NlN R`TkT RRKRN RRK^K^N ReCeG ReNe[ RWT\\Y RTSWT RZTS[ RSGTJ RWFXJ RbIcM RiIhM R\\FZK RSP\\PXW R]DRE',
	    'Po`GkG RSH^H R_NlN RTQ]Q R`TkT RTY^Y RSDSH RTMT[ RTM]M]Q RTU^U^[ R^D^H ReCeG ReNe[ RbIbM RiIhM RXJXM RWJRL RXCXI',
	    'PoRGZG RcGlG RRN[N RcNlN RcTlT RTITM RWCWG RYIXM R[JbJbY ReIeM RhChG RhNh[ RjIiM Ra[^[ RWNVTVY R^C^N\\X R[SRT',
	    'PoRGZG RcGlG RRN[N RcNlN RcTlT RTITM RWCWG RYIXM ReIeM RhChG RhNh[ RjIiM R]SY[ RaZ]Z RWNVTVY R^E^N\\W R[EbEbVa[ R[SRT',
	    'PoRG[G RcGlG RRN[N RbNlN RcTlT RTIUM RdIeM RgCgG RgNg[ RjIiM RWCWG RWNWTVY RYIYM R]I\\Q RaDaQ`W R[SRT',
	    'PoRG[G RcGlG RRN[N RbNlN RRS[S RcTlT R\\L_L_X RdIeM RgNg[ R^D_H RbV_X RTITM RVCWG RfChG RjIhN RYIWNWTVY',
	    'PoYF^F R`GkG RRJ_J R_NlN RTR]R R`TkT RTY^Y RTMT[ RUEUJ RTM]M]R RTU^U^[ ReNe[ RdDeG RYCXJWN RbIcM RiIgN',
	    'PodGlG RRGZG RRNZN RdNlN RRSZS RdSlS RTITM ReIfM RhNh[ R_O\\R RUCWG RgChG R`D\\L RbI_P RjHiN RbW[X RbR\\S RXIVOVTUY RaK\\L',
	    'PodGlG RRNZN RdNlN RRSZS RdSlS RTITM ReIfM RhNh[ RUCWG R]MbX R]C_G RgChG R`O[Y RjIiN RRGZHbI]V RXJVOVTUY',
	    'PoaGkG R`NlN RUR\\R RaTkT RVT[T RSNS[ RfCfG RfNf[ RXV[Y R\\F_I R[UVZ RXFXH RcIcM RWL\\N RiHhM RUERJ RXNVR RUJ]JYL RSN_N_[\\[ R]DTE',
	    'PoRGZG RcGlG RcNlN RRNZObP RcTlT RTITM RYIXM R`O`[ ReIeM RhChG RhNh[ R[I^K RaMbP RjIiM R^C\\I R`G\\O RVCVG RaSbW R[S[X RZSRT RVNVTUY',
	    'Po\\EbE RRGZG RcGlG R[HcH R\\LbL RRNZN RcNlN R\\PbP RcTlT R\\YbY RTITM RVNVW RYIXM R\\S\\Z R\\SbSbY ReIeM RhChG RhNh[ RjIiM RVCVG RZSRT',
	    'PoUEkE RYJhJ RUOlO R[O[Z R`OcUfYl[ RhScU RcYWZ RUEUQTY',
	    'PoUEjE RYHhH RULlL RZQaQ RWQ^Q RRTlT R_[e[ RZKZQ ReRe[ RWW\\Y R_KbNfPlR RiMcN RUEUJTP',
	    'PoUIiI RULiL RUOkO RXQhQ RUUlU RUEUL RYTYZ R\\C\\L RcCcL RUEiEiL R^TaXeYl[ RiVbX RUOUTTZ R`YX[',
	    'PoRF]F R_FkF RTNkN RXQiQ RTTlT RXCXL RZTZ[ ReCeL RXF[I RYFSL ReF_L RdFfHjJ R_TcXgZl[ RiVcW RbYXZ RTNTTSZ',
	    'Po`EkE RYI]I RbIjI R`NlN RSO\\O RSS\\S RRW]W RSKSS RWDWI RXKX[ RYDYI RSK]K]S RYD]D]I RcNcZ RhWl[ RkQgU RfRjZ ReNhX RgY`[ RWDSESIWI R`E`R^Z',
	    'Po_SiS R_[l[ R`N`Z RXRTZ RWT\\Ye[ R[EUF RfDXF RgGWJ RYNSO RjKYN',
	    'Po\\[l[ RRPWPWWS[ RTDXF RRJXL RZZb[',
	    'PoZLlL R\\[l[ RRPWPWWS[ RTDXF RRJXL RZZb[',
	    'PodViV R\\[l[ RRPWPWWR[ RTDXF RRJWL R_E^NZV RYZa[ RZEfEeLkKkOjV R^H]R',
	    'PocWhW R\\[l[ RTDXH RRMWMWWR[ R_R[W RVX[Za[ R[IjIjOiW RbDaJ`R',
	    'PodWhW RTDWH RRNWNWWS[ RZEjEjPiW RUXa[l[ RaE`N]S[V',
	    'PoZLlL R\\[l[ RbCbX RRPWPWWS[ RTDXF RRJXL RZZb[',
	    'PoTDWH RRNWNWWS[ R]EcEdMgRkW RcJ_R[V RUYa[k[',
	    'Po_XcX R\\[l[ RcKcW RTDWH RRMWMWWR[ RZFjFeJ RVX[Za[',
	    'PoZJkJ R\\[l[ ReRjV RRPWPWWS[ R`R[W RbCcMfT RTDXF RRJXL RbIeR RZZb[ RbFaR',
	    'Po[UjU R\\[l[ R[H[W RcDcU RjHjW RRPWPWWR[ RTDXF RRJWL RYZa[',
	    'Po[EkE RZMlM R\\[l[ RcEcX RRPWPWWS[ RTDXF RRJXL RZZb[',
	    'PoZMlM R\\[l[ RcEcX RRPWPWWS[ RTDXF RRJXL RZZb[ RiD\\F',
	    'Po[EkE RZMlM R^WcW R\\[l[ RcEcW RRPWPWWS[ RTDXF RRJXL RZZb[',
	    'Po\\EiE RZJlJ RbXgX R\\[l[ RRPWPWWR[ RTDXF RSJWL R_J]PjPhW RVX[Za[',
	    'Po]HlH R]VkV R\\[l[ RkSkV RRPWPWWR[ RaP\\T R]GZJ RTDXF RRJWL R^C\\I R]LhLdNaO RYZa[',
	    'PoZNfN R`G`X RTDWH RRNWNWWS[ RUX_[k[ RZEiEiPiWkXlU',
	    'PofQiQ R]VjV R\\[l[ R]E]U RcCcR RjGjP RRPWPWWR[ RkSjV RTDXF RRJWL RiGYL RYZa[',
	    'PoZIkI RaXfX R\\[l[ RfCfW RTDXH R\\M_P`R RUXR[ RRNWNWWYY\\Za[',
	    'PoZFlF RcWhW R\\[l[ RTDWH RRNWNWWS[ R^RZW R`M\\U RZZa[ R`LjLjQhV RaF_R',
	    'Po]MjM R\\[l[ R]EjEjM RTDXH RUXR[ RROWOWWYY\\Za[ R]E]N\\V',
	    'PoZElE RfVjV R\\[l[ R\\J\\V RcEcY R\\JjJjU RRPWPWWR[ RTDXF RRJWL RYZa[',
	    'Po[EkE R\\MjM RZVlV R\\[l[ RcEcV RRPWPWWS[ RTDXF RRJXL RYZb[',
	    'Po^JjJ R]QjQ RfXjX R\\[l[ R^EjEjW RSFXH RUXR[ R[VYW RRMWMWX[Za[ R^E]O\\T',
	    'Po[GbG R[QjQ RbWkW R\\[l[ R[J[Q RcCcW RjJjQ RRPWPWWR[ RTDWF RRJWL RYZa[ RjE_G',
	    'PogUkU R[F[U RdFdY RdFkFkT RSDWH RRNVNVWR[ RbD[E RaSZT RUYa[m[',
	    'PoZJlJ R\\[l[ RaSeV RSEWH RUXR[ R`QZW RbKiSjV RbK^T RRMWMWX[Za[ RcDaP',
	    'Po\\EjE RZLlL R\\[l[ R[V`VcUfUjTkW RfPjS RRPWPWWR[ RTDXF RSJWL RaL]V RYZa[',
	    'Po]LlL RfLfX RTDWH RRNWNWWS[ R]E]N\\U RiC]E RUYa[l[',
	    'PoZGlG RgWlW R\\[l[ RcCcG R^KgKhW RlTlW RRPWPWWR[ RTDXF RSJWL R\\WZW RYZa[ R^K^P]U',
	    'Po^EkE RZMlM RcXgX R\\[l[ R^E^M RhEhW RRPWPWWR[ RTDXF RRJWL RfMaSZV RYZa[',
	    'Po\\EkE RSDWH RRNVNVWR[ R_KaQfVlX R\\KjKgPcU]W R\\E\\N[U RUYa[k[',
	    'Po\\HkH RYOlO R\\[l[ RcGcY RRPWPWWS[ RTDXF RRJXL R^CZL RZZb[',
	    'PoZIlI R\\[l[ RRNWNWWYY RhRkT RcCbLiR RfDiG RaP[V RVXR[ RTDVI RbK_R RZZa[ RbFaO',
	    'Po\\FjF RZMkM R\\[l[ RSEWH RbPeQjW RaQ[W RcFbN_T RWXSZ RRMWMWX[Za[ RbIaQ',
	    'PoZEkE R\\[l[ RcIcX RTDXH ReLkQ RRMWMWWR[ RbJ[Q RdFaK RVX[Za[',
	    'PoYHlH R[[l[ R[L^M RSEWH ReSjW RaCbG RRNWNWXRZ R_OcQfS RhHeO`TYW RVY[Za[',
	    'Po[QkQ R\\[l[ R[H[Q RcCcX R[HkHlQ RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'Po[IkI RZPlP R\\[l[ RgDgX RTDXH RUXR[ RRMWMWX\\Za[ R_D_P^U',
	    'Po\\EjE RZKlK ReWkW R\\[l[ RfKfW RkSkW RUXR[ R^SZW RSFXH R_K^T RRMVMWX[Za[',
	    'PoZFkF R[KjK RfVjV R[[l[ RbCbX RSEWI RRNVNWXRZ RYPkPjV RVY[Za[',
	    'PoZFjF R[NjN RZSkS R[[l[ RcIcY RSDWH RRMWMWWR[ R`C\\M RVX[Za[',
	    'Po]MiM R\\[l[ R]EiEiL RTDWH RcOjW RUXR[ RRNWNWX\\Za[ R]E]N\\U',
	    'PobHkH R\\VjV R\\[l[ R\\N\\V RbCbN R\\NjNjV RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'Po\\JjJ RaWkW R\\[l[ RaMaV R\\EjEkJ RTDXH RkSkV RUXR[ RjO`Q RROWOWWYY\\Za[ R\\E\\OZW',
	    'PoeLiL R\\WjW R\\[l[ R\\O\\W R\\OjOjW RRPWPWWR[ RTDXF RSJWL RbE`J^LZM R[EjEjL RYZa[',
	    'PoYJlJ RbQhQ R\\VkV R\\[l[ R\\D\\X RbCbQ RhDhQ RRPWPWWR[ RTDXF RRJWL RYZa[',
	    'Po\\GlG RgSiS R^WkW R\\[l[ R^K^W RcIcS RjLjS RRPWPWWR[ RkTkW RTDXF RSJWL R^C[J RiLYO RYZa[',
	    'Po`QfQ RfWjW R\\[l[ R[E[X R`J`S R`JfJfQ R[EjEjW RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'PoeWkW R\\[l[ ReGeW ReGkGkW RRPWPWWR[ RTDXF RRJWL R`W]W RYZa[ R]D]L[U RYHaHaS`W',
	    'Po[MjM R[RjR R[[l[ R[H[R RcCcX R[HjHjR RSEWH RUXR[ RRMVMVX[Za[',
	    'PoaKjK R]KfK RZKcK R\\WiW R\\[l[ R\\O\\W R\\OiOiW RfFlL RRPWPWWR[ RTDXF RRJWL RaC^J RYZa[',
	    'Po]HkH R_XcX R\\[l[ RcGcX RTDWH RRNWNWWS[ RhMlT R^C[L R^M[T RZZa[',
	    'Po[OjO R[WjW R\\[l[ R[H[V RcCcV R[HjHkV RSDWH RRNVNVWR[ RYZa[',
	    'Po\\OjO R\\VjV R\\H\\V R\\HjHjV RTDWH RRNWNWWS[ RcCaH RUXa[l[',
	    'Po[IkI R\\OjO R[VlV R\\[l[ RcIcV R`DdE RRPWPWWR[ RTDXF RSJWL RVX[Za[',
	    'Po]IjI RZNlN RTDWH RRNWNWWS[ R^D\\L RUXa[l[ RcCcMaR\\W RcNeRjW',
	    'Po]HlH RbMkM RbRkR R\\[l[ RbHbX RRPWPWWR[ RTDXF RSJWL R_CZM RYZa[',
	    'Po\\GbG R[[k[ R\\M`P RfCfX RZL_N RfLiMlP RRPVPVWR[ R^SZW RTDXF RSJWL RaL]V R]CZM RbH_S RYZa[',
	    'PoZIlI RcDcX RTDWH RhDjF RRNWNWWS[ RhLkU R^MZU RUX`[l[',
	    'Po^WjW R\\[l[ RZGZJ R^J^V RZGkGkJ RRPWPWWR[ RjSjW RTDXF RRJWL RcCcG RhL]O RYZa[',
	    'Po\\HjH RZNlN R\\[l[ RcDcN R[VbViUjW RfRjU RRPWPWWR[ RTDXF RRJWL RaN]V RVX[Za[',
	    'Po\\OjO RZVkV R\\[l[ RcOcV RSEVI RUXR[ RRNWNWX][ RcJkL R[EhEeI`KZL',
	    'Po_QfQ R[VjV R\\[l[ R[E[V R_J_Q R_JfJfQ R[EjEjV RRPVPVWR[ RTDXF RSJWL RYZa[',
	    'Po^HhH R_RfR RgWjW R\\[l[ R[E[W R_L_T R_LfLfR R[EjEjW RRPWPWWR[ RTDWF RRJWL RYZa[',
	    'PoZGlG R^OiO R^SiS RfXiX R\\[l[ R_J_X R]LiKiW RRPWPWWR[ R_JYO RTDXF RSJWL RaC]M RYZa[',
	    'PoZMlM RbDbX RTDWH RRNWNWWS[ R\\E^J RiEgJ RbM`P[V RVX`[m[ RcMePkV',
	    'PoZHkH RYOlO R\\[l[ RfHfX R^C_G RRPWPWWR[ RgCfG RTDXF RSJWL RYZa[ R_H_P^U',
	    'Po[HlH RbXfX R\\[l[ RcCcH RfHfX RRPWPWWR[ R_T[X RiLlS RTDXF RSJWL R\\LYS RYZa[ RaHaN_T',
	    'PoZElE ReQkQ R[WkW R\\[l[ R[J[W ReEeQ R[JkJkW RRPWPWWR[ R`E`O\\R RTDXF RSJWL RYZa[',
	    'Po\\[l[ R^HcJ RRPWPWWR[ RTDXF RSJWL ReJ\\O R`FiFdLbP[R RbOkPfU_X RbC_GZI R`QcReU\\X RYZa[',
	    'Po[HkH R[MjM R]WiW R\\[l[ R]Q]W RbDbM R]QiQiW RTDWH RUXR[ RROWOWWYY\\Za[',
	    'Po\\LiL R\\WjW R\\F\\W R\\FiFiL R\\PjPjW RTDWH RRNWNWWS[ RaCaF RUYa[m[',
	    'PoZDcD R\\[l[ ReFeR R]M`Q RRPWPWWR[ R_RZW RTDXF RSJWL R]GYP RbL^U R]E[N R\\JbJ`S RjDjXgX RVX[Za[',
	    'Po]PeP R]UeU R[[l[ R]K]T R]KfKfT RSDWH R^C[L RRNWNWXRZ RiWfW RVY\\Z`[ R]GkGjRiX',
	    'Po]IiI R]NiN R]E]W R]EiEiN RTEXH RRNWNWWS[ RcNeShVlW RjPeR RdVZW RUY`[l[',
	    'Po[IkI RZOlO RTDWH RRNWNWWS[ RhCfH R]C_G RUY_[l[ RcIcOaS^VZW RcOeShVkW',
	    'Po[EaE RZJlJ R\\WjW R\\[l[ R\\P\\W RcEcP R\\PjPjW RRPWPWWR[ RTDXF RSJWL RYZa[ RiD`E',
	    'PoSDWH RRNVNVWR[ R[F\\K RfOjP RjFhJ R_OYR RUY_[l[ R`C`O_SZW ReCeWkWlU',
	    'Po]PiP RYUkU R\\[l[ R]P]U RdMdY RRPWPWWR[ RaC[I RTDXF RSJWL R^GaJfLlM R_FhFeJ`LYN RVX[Za[',
	    'Po[JlJ R^WkW R\\[l[ R^O^W R^OkOkW RRPWPWWR[ RTDXF RSJWL RYZa[ R[E[NZU RbE[E RjC^E',
	    'PoZHlH R\\SjS R\\K\\R RjKjR RSDWH R]C_G RRNVNVWR[ RhCfG RUY`[l[ RcHcRaU^X',
	    'PoYJ_J RbWkW R\\[l[ R\\D\\T RbEbV RfCfS RlSkW RUXR[ RSEWG RjH_K R`SXU RjHjQhQ RRMVMWX[Za[',
	    'Po_RfR R\\[l[ R[G[X R_L_S R_LfLfR RRPWPWWR[ RTDXF RSJWL R[GjGjXgX RYZa[ RbCbH',
	    'Po]HjH RZMlM ReWkW R[[l[ RcCcM RfMfW RlSkW RSDWH RRNVNVXRZ R_S[V R^D[J R`M^T RVX[Za[',
	    'PocXfX R[[l[ RgDgX RSEWH RWXSZ RjIkS R`MYO RYEaE]I]XZX RcIaT RRMWMWX[Za[',
	    'PoZFlF R[NjN R[SjS RgXjX R[[l[ R[J[X RbCbX R[JjJjX RRPVPVWR[ RSDWF RRJWL RfDiE RYZa[',
	    'PobFkF R[SeS R[WjW R\\[l[ R[J[W RbCbJ R[NeNeR R[JjJjW RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'Po]OiO R]SiS ReXiX R\\[l[ R]K]X RcCcK R]KiKiX RgElI RRPWPWWR[ R_EZJ RTDXF RSJWL RYZa[',
	    'PoZElE ReOkO R[SkS R[WkW R\\[l[ R[J[W ReEeO R[JkJkW R`E`M\\P RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'PoZHlH ReXiX RSDWH RRNVNVWR[ RcHgMlO RcH^MYO R\\OfOfSjRiX RiDaE]E RcEcN RUX_[l[ RaP_T[W',
	    'PoYElE RSDWH RRNVNVWR[ RcE`IYL RUX_[l[ R`IeMeWaX RiIeM ReNmU RcL`N[P RdNaSYV',
	    'PoZHlH R\\[l[ R[K_N RfQkV RfDkF RRPWPWWR[ RkJgN RcJgS RTDXF RSJWL R`RYU RcGeQ RcCcX_X RYZa[',
	    'Po\\LjL RgUjU R\\[l[ RcGcY R[GjGjL RSEWH R^D`F RRNWNVXR[ RgCeG RcP^TZV R[L[PkPjU RZZa[',
	    'Po[IlI R[LlL RgWjW R\\[l[ R^P^W RdDdY R^PjPjW RSDWH RRNVNVWR[ RYZa[ R[E[OZV RbE[E RiC^E',
	    'Po^KhK RZOlO R_XbX RcKcX RSDWH RgRkV RcChIlJ RRNVNVWR[ R^RYV RcC\\IYJ RUX_[l[',
	    'PoZElE R[PjP RYWlW R\\[l[ RbPbW R\\K^O RaKcM RgKjN RRPWPWWR[ R]F[J RcFbJ RiGgJ RTDXF RSJWL RYZa[',
	    'Po[[l[ R^J^V RRPVPVWR[ R_EYK R^MYS RgCgOjW RSDWF RRJWL ReObW RkHhM RbHaN R]XZX RfIgS RfJdT R]G^P RYZa[',
	    'Po[ElE R\\OjO RZWlW R\\[l[ R\\I\\O R\\IjIjO RRPWPWWR[ R^Q`U RTDXF RRJWL RgQfU RYZa[',
	    'PoZHkH R]LjL RYQlQ R\\[l[ RhFkI RRPWPWWR[ RTDXF RSJWL RbOdSgVlX RfEjG R`D^H R]I[O RbS_VZX RYZa[ RbMaT',
	    'Po]EjE RZHlH R\\KjK R\\NjN R\\WjW R\\[l[ R\\R\\W R\\RjRjW RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'Po\\NjN R\\SjS RfXjX R\\J\\X RcJcX R\\JjJjX RSDWH RRNWNWWSZ R_GgJ R[EjEcH RVX`[m[',
	    'PoaEkE RbMkM R`VlV R\\[l[ R^I^V RfEfV RRQWQWWR[ R_EYJ R^MYQ RTDXF RSJWL R]W[W R]G^O RYZa[',
	    'PoZEkE RYNlN R\\WjW R[[l[ R\\Q\\W R[IhIhN R\\QjQjW RSDWH RRNVNWXRZ RaE^N RVY[Za[',
	    'PoYIaI RcLlL RZX]X R]C]X RhLhX RSDWH RRNVNVWR[ RaNYP RkDcEcPaW RUX_[l[',
	    'Po\\JiJ R[NkN R[RjR RZWlW R\\[l[ R\\D\\J RcNcW R\\DiDiJ RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'PoZGlG R[PjP R[J[P RcCcY R[JjJjP RTDWH RRNWNWWS[ RcP\\W RcPjWlW RVX`[m[',
	    'Po\\HjH RZMlM R\\WiW R\\P\\W RcCcM R\\PiPiW RTDWH RRNWNWWS[ R^D[J RVX`[m[',
	    'Po\\HjH RdMkM R\\[l[ ReHeM RhFkI RRPWPWWR[ RaN[S R_J[N RTDXF RSJWL ReEjG R_E]HZH R^QbTfWlX R^QhPdU_WZX RYZa[',
	    'Po\\OhO R[RiR RZVkV R\\[l[ RbMbY RRPVPVWR[ R`CZI RTDXF RRJWL R^FaIeLlM R^FgFdI_LYM RYZa[',
	    'PoYFkF R[MjM R[QjQ RYUlU R[J[Q RbCbY R[JjJjQ RSDWH RRNVNVWR[ RUX_[l[',
	    'Po[[l[ ReDeO RYJ\\L RSEWH RZE]F RUXR[ RiGlM R\\PYW RbG_N RiNgRdU]X RRMVMVX[Za[',
	    'Po`JeJ R`PeP R[WjW R\\[l[ R[D[W R`D`W ReDeW R[DjDjW RTDWG RRMWMWWR[ RVX[Za[',
	    'PoYEkE R^XaX RhXjX R\\[l[ R[I[W R[IaIaW RdIdX RdIjIjW RRMWMWWR[ RUDVI R\\M^R RfMhR RVX[Za[',
	    'Po\\WiW R\\[l[ R\\O\\W R\\OiOiW RcGhMlO RfDkH RRPWPWWR[ R_DZH RbG[N RTDXF RSJWL RYZa[',
	    'PoZGlG R\\[l[ RcCcY R]K`N RgKkO RRPWPWWR[ RTDXF RRJWL RcNhTlV R^H[O RhHeN RcO]UYV RYZa[',
	    'PoZFlF R]LgL RZRlR R\\[l[ RbIbO RfNfX RdFhJlL RRPWPWWR[ RaF[L RTDXF RSJWL RaC^I RVX[Za[ R^P]V',
	    'PoZGkG RYKlK R\\SiS R\\WiW R\\[l[ R\\O\\W R_C_K RfCfK R\\OiOiW RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'PocEkE RcOkO R[[l[ RSDWH R[QaW RdSgTkX R[G`L RdHgIkM RRNVNVYR[ R`R[W RjQdX RYOaO^T RZEaE_H\\LYM RjFeKbM RVY[Za[',
	    'PoZIbI RdIkI RcPlP R\\[l[ RgIgX RRPWPWWR[ RTDXF RRJWL RhCeI RbNYP RYZa[ R^C^I^Q]V',
	    'Po]NiN R]WjW R\\[l[ RZFZI R]I]W RbCcF R]IiIiN R]RjRjW RZFlFlI RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'PoYJlJ R[MiM R[FiFiM RSDWH R[P^R RRNVNVWR[ RjOfS RbMdRgUlW R_TYW RbCbX_X RUX_[l[',
	    'Po]IgI RZLlL R_XcX R\\[l[ RcLcX R\\N_Q RRPWPWWR[ RiNfQ RTDXF RRJWL RcRiT RaSZV R^C]J R^FgFfL RYZa[',
	    'PodGlG RYHaH RbPlP RZX]X RdXgX R\\[l[ R]C]W RhNhX RUXR[ RdKkKgN RSFXH ReCbJ RaNYP RRMVMWX[Za[',
	    'Po^IgI R^MhM R_TfT RfXjX R_O_V RcFcL R_OfOfT R[EjEjX RSDWH RRNVNVWR[ R[E[PZW RUX_[l[',
	    'Po\\IkI R\\MjM R\\RjR R\\WlW R]G]W RdFdW RSDWH RRNVNVWR[ R_DZJ ReDdI RUX_[l[',
	    'PoZFlF R[KjK R]PiP R]TiT RfXiX R\\[l[ R]M]X RcCcF R]MiMiX RgHkK RRPWPWWR[ RTDXF RRJWL R`F^J RYZa[',
	    'PobFlF R[MjM R[QjQ RYUlU R\\[l[ R[J[Q RbCbJ RbQbY R[JjJjQ RRPWPWWR[ RTDXF RSJWL RYZa[',
	    'Po[FjF RYJlJ R[RjR RYWlW R\\[l[ RbCbJ RbObW RRPWPWWR[ R_L\\O RTDXF RSJWL ReJeNkMkN RYZa[',
	    'PoZHlH RZQlQ R\\[l[ R[EcEcM RRPWPWWR[ RcGfKlM RTDXF RSJWL RcG_KYM R_O^TjX RgRdUaW[X RVX[Za[ RiCaD',
	    'Po\\HiH R\\KiK R\\[l[ R\\D\\K R\\DiDiK RRPWPWWR[ RaQ[W ReT`X R^NYS RTDXF RSJWL RfOcV RiXeX RYZa[ R_L]PjOiX',
	    'Po\\OjO R\\J\\O R[JjJjO RTDWH RRNWNWWS[ R^EYJ R^FfFcJ RVX`[m[ RcJcP RePeWkWkT R_P^T[W',
	    'Po[FjF RYJlJ R[OjO RZTkT R\\[l[ RbCbJ RbObY R^K_N RRPWPWWR[ RTDXF RRJWL RgKfN RYZa[',
	    'Po[FjF RYIlI R\\[l[ R_R_X R[OcPfSkX RUXR[ RbCbI\\O RSFXH RgLbPZT RkQfS RfW^X RRMWMWX[Za[',
	    'Po[JkJ R_NiN R\\[l[ R[D[J RaDaJ RfDfJ R[DkDkJ RTDXH RaL[R RiPcU RVXR[ RaRdT\\X RRMWMWX\\Zb[',
	    'PoZDkD R\\LiL R[SkS R[WkW R\\[l[ R[O[W R\\H\\L RcOcW R\\HiHiL R[OkOkW RRPWPWWR[ RTDWF RRJWL RYZa[',
	    'PoZFlF RZLlL R^WjW R\\[l[ R^O^W R_D_H RfDfH R]QjQjW RTDXH RaLZS RUXR[ RbH_O RROWOWWYY\\Za[',
	    'Po^JfJ R[[l[ R[M[X R]O_P R]R_T RRPVPVWR[ RcRfX RhRjX RSDWF RSJWL R\\EcEfHlJ RjLgR RfMcR RaD^HXJ R[MaM_X`X^X RVXZZa[',
	    'Po\\GiG R\\KiK RYNlN ReXiX R\\[l[ R\\D\\K R\\DiDiK RRPWPWWR[ R_OZT RaR[X ReT`X RTDXF RRJWL RfQcW R]QjQiX RYZa[',
	    'Po[FkF R_IiI R]LkL R]RiR R]UiU ReYiY R[[l[ R]N]X RdIdL R]NiNiX RSEWH RUXR[ R`FZL R`C^H RRMWMWX[Za[',
	    'Po[IlI R_PjP R_TjT R_WjW R_M_W RdEdM R_MjMjW RRPWPWWR[ RTDXF RSJWL R[E[OZV RUX_[l[ RjCbE[E',
	    'PoYHlH RSDWH R]C^G RRNVNVWR[ RhDeG RaJYN RbN[Q RiJfMdPaSZW RUX_[l[ R`KdPeSdW_X RdPmU',
	    'Po[ElE R[LlL R\\[l[ RcCcF RRPWPWWR[ RTDXF RRJWL R^GhJ RfG\\J RgM^P RgQ^T RkSfV^X RYZa[ R[L[PZW',
	    'Po\\IkI R[LlL RhXjX R\\[l[ R\\P\\X R\\E\\I RaPaW RcCcI RfPfW RkEkI R\\PkPkX RRPWPWWR[ RTDXF RSJWL RcLaQ RYZa[',
	    'Po[IkI R]MkM R^QjQ R]TlT RdMdX R[EkEkI RSDWH R`JaL RRNWNWWR[ RhJgL R[E[OZV RVX`[m[',
	    'Po[FkF R\\[l[ R[L[N R\\P\\W RcMcY R[LkLkN RTEWH RROWOWWYY RbDdF RUXR[ R_H_J RgFeL R\\PiPiVfV RZZa[',
	    'Po\\HiH R\\LiL R]UhU RgXkX R[O[X R\\D\\L RbDbT R\\DiDiL R[OkOkX RSDWH RRNVNVWR[ RfQhV RUX_[l[',
	    'Po]HiH R]KiK R[NlN RcSjS RcXlX R\\[l[ R]D]K RdNdW R]DiDiK RSEWH RUXR[ R^Q[X R^SaWiX RRMWMWX[Za[',
	    'PobFlF R\\MjM R\\QjQ R\\TjT R\\[l[ R\\J\\T RbCbJ R\\JjJjT RRPWPWWR[ RTDXF RSJWL RfUlX R`V[X RYZa[',
	    'PodGlG RYGbG RcQlQ R]X`X RdXgX R\\LaL`X RgNgX RSDWH RRNVNVWR[ RdKkKgO ReCbJ R]C]G\\O[V RUX_[l[',
	    'PoZIkI R\\OiO R\\SiS RYVlV R\\L\\R RbFbY R\\LiLiR RYGYEkEkG RSDWH RRNVNVWR[ RUX_[l[',
	    'Po[IaI RdIkI R[LjL RYOlO RcYgY R\\[l[ R[D[I R[DaDaI RdDdI RdDkDkI RRPWPWWR[ RTDXF RSJWL R^O]SiShX RYZa[',
	    'PoYElE R[LjL R]SkS RbObX RfOfX R[HjHjL RSDWH RRNVNVWR[ R]OkOkXhX RUX_[l[ R[H[LZU R]O]X',
	    'PobHhH R_UfU RgXkX R[M[X R_P_V RbHbM R_PfPfU R]DhDhM R]D\\MkMkX RSDWH RRNVNVWR[ RUX_[l[',
	    'Po\\HiH R\\KiK R]VgV R\\[l[ R\\D\\K R]P]W R\\DiDiK RbQeT RRPWPWWR[ RbQ_T R_LZR RTDXF RSJWL R^OkOjXgX RYZa[',
	    'PoZJaJ RdJkJ RZOaO RZSaS R[[l[ RZDZX RZDaDaJ RdDkDkJ RfSkX RRPVPVWR[ RdOgU RSDXF RRJWL RcNkNhSeVaX RYZa[',
	    'Po\\IiI R\\LiL R[OkO R[SjS RZWlW R\\[l[ R\\F\\L RcOcW R\\FiFiL RRPWPWWR[ RTDXF RSJWL RbCaE RYZa[',
	    'Po\\HhH RdPjP R[SjS R[WjW R[[l[ R[L[W ReHeP R[LjLjW R^EgElJ RRPVPVWR[ R`H`N]Q R]FXJ RSDWF RRJWL RYZa[',
	    'PoZGlG R\\OiO R\\SiS R\\WiW R\\K\\W RcGaK R\\KiKiW RTDWH R^C_F RRNWNWWS[ RgCfG RVX`[m[',
	    'Po[FjF RYJlJ RZNkN R[QjQ RYUlU RbCbI RbNbX RSDWH RRNVNVWR[ R^K^N RfJfM RUX_[l[',
	    'PoYIlI R\\OhO RZRkR RYVlV RdQdY R\\LhLhP RSDWH RRNVNVWR[ RaC_I R]R]V R\\L\\P R[FhFhI RUX_[l[',
	    'PoZFlF R^OgO R^SgS RgXjX R[[l[ R[J[X RcCcJ RcOcW R[JjJjX RRPVPVWR[ R_L`N RSDXF RRJWL RfKeN RYZa[',
	    'Po[HiH RYKkK R\\[l[ R[E[I R\\N\\T RbCbK R\\NiNiT R[EiEiI RTDXH RRMWMWWR[ RaU[X RdVkW RbPbU RVX[Za[',
	    'PoZEkE R[HjH RYKlK R\\QiQ RYTlT ReYiY R[[l[ R\\N\\X R_C_K RbKbT RfCfK R\\NiNiX RRPVPVWR[ RSDXF RRJWL RYZa[',
	    'Po[H`J R^NjN RZQlQ R]WjW R\\[l[ R]S]X RcMcW RjSjX RRPWPWWR[ R_M\\O RTDXF RRJWL R_FdH RcCZH R`FiEfHaKZM RYZa[',
	    'Po\\I`I ReIjI R\\MjM R[[l[ R\\E\\M RcDcP ReEjEjM RSEWH RUXR[ R`SjX R]SgU R[PjPgTbVZX RaE[E RRMVMVX[Za[',
	    'Po[SjS R[WjW R\\[l[ R[E[L R[O[W RcOcW R[OjOjW RTDWF RRPWPWWR[ RRJWL R`GaJYM RfEdL RgLjLfL RaDZE RYZa[ RcEkEjL',
	    'Po`PkP RZX\\X R[[l[ R]I]W RfOfX RbIeL RiMlP RRPVPVWR[ RiHbO RYE`E\\I RfFcI RiRkW RSDWF RRJWL RbR`W R`MXO RkDaE RYZa[',
	    'PoZIjI R\\[l[ RZDZI R_D_I RbKbY ReDeI RZDjDjI RfQkV RgLjP RRPWPWWR[ R_QYW R^LZP RTDXF RSJWL RYZa[',
	    'PodGjG R`MfM RdOjO R[[l[ RdEdO RRPVPVWR[ RSDWF RRJWL R^JkJkL RgRgXkWkX RaX_X RcRaW RVXZZa[ R[E[OYU R^J^P]V RiC\\E',
	    'Po[IkI R]MjM R]QkQ R^UkU R[[l[ RdQdY RSDWH RcJeL RRNVNVYR[ RaNaP RgNfQ R[EjEjI RVY[Za[ R[E[OYW',
	    'Po[FjF RYIlI R\\QiQ R\\L\\Q RbQbX RbCbI R\\LiLiQ RSDWH RRNVNVWR[ RbQfUkW RbQ_TYV RjReT RUX_[l[',
	    'PoXHbH ReJkJ RePkP RYRaR RgXkX RYKYR RaKaR ReDkDkX RRPVPVWR[ RZC[G RSDWF RRJWL RaC_G R]H]Q\\V ReDePcV RUX_[l[',
	    'Po^GfG R^JfJ R[[l[ R\\D\\K R\\DiDiK RcMjMjX RRPVPVWR[ R_QZW RhMcQ RiQcV R^MZQ RSDXF RRJWL RYMaMaX]X RgXjXgX RYZa[',
	    'Po[HiH RYKlK R\\QhQ R\\XiX R[E[H R\\N\\W RbCbK R\\NhNhQ R[EiEiH R\\TiTiX RSDWH RRNVNVWR[ RUX_[l[',
	    'Po\\DjD R\\HiH R\\LiL ReXjX R\\[l[ R_R_V RcDcO RTDXH RRMWMWWR[ RcRdU RgRgT R[RYV RVX[Za[ R\\D\\OkOjX',
	    'PoZKkK RYPlP R\\XjX Ra[l[ R\\S\\X RbKbX RjSjX RSDWH R\\G]J R[EbFcI RRNVNVWR[ RhGfJ RUYa[ RiCaD',
	    'PoZGlG R\\OjO R\\QjQ R\\TjT R\\[l[ R\\L\\T R]DiDiJ R]D\\JcKjLjT RRPWPWWR[ RTDXF RSJWL RfVlX R`VZX RcDbJ RYZa[',
	    'Po[FkF RYJlJ R\\OiO R\\RiR RYUlU R\\[l[ R\\L\\R RcCcF RcRcY R\\LiLiR RRPWPWWR[ R_F_J RTDXF RSJWL RgFeJ RYZa[',
	    'PoZFaF RYJbJ RYNbN R[[l[ R]C]J RRPVPVWR[ RgSbX RfNjW RSDWF RRJWL RiMeV RdIhT RdIkHgT ReCcM R`X]X R]K]N[W RVXZZa[ R\\QaQ`X',
	    'PoYFlF R]NhN R_UfU RgXjX R[K[X R_Q_V RcCbF RcKcQ R_QfQfU R[KjKjW RSDWH RRNVNVWR[ R^G_J RgGfJ RUX_[l[',
	    'PocLkL RYL`L R\\[l[ RSEWH RiJkL R[GgFiG RfHcK R_E[G R\\IYL RVWR[ RbLYP RbLfOlP RbPZR RfQaT[U RjSdV[Y RROVOVWXY[Z`[',
	    'PoYFcF ReIlI RZOaO R[[l[ RZJZO R^C^X RZJaJaO RSEWI R]ObT RgTbX RROVOVWR[ RfMhQkW RiNeV R]NZU RiJfT ReDdM RVX[Z`[',
	    'PoYGbG RYOaO R[[l[ RYJYO R^C^X RYJaJbO R]ObT RRPVPVWR[ R\\QXV ReTaX RSDWF RRJWK RgHgPdV R]N[S RgNiU RdHkHkM RgMfT RgKhT ReDcM RVXZZa[',
	    'PoYFlF R[KjK R[OjO R]TiT R]XiX R[H[N R]Q]X R`C`N ReCeN R]QiQiX R[HjHkN RSDWH RRNVNVWR[ RUX_[l[',
	    'Po[FlF R]KlK RaQhQ RaHaQ RbSbX RcCcG RhHhQ RSDWH RRNVNVWR[ RfSfX RjTkX R^S]X R[F[OZU RUX_[l[',
	    'PoaElE RZK^K RZQ^Q R\\X^X R[[l[ RZE^E^X RfMfW RSDVG RSJVL RRPVPUWR[ ReK`O RfO`U RfE`K RkIhM ReXbX ReJfS RgEiQ RUXZZ`[ RZEZPXW',
	    'PoYFlF R`JdJ RhKkK RfWiW R[[l[ RYMYP R\\P\\W R`C`J RbMbY RdCdJ RhChJ R\\PiPiW RYMkMkP RTDWG RSJVL RRPVPVWR[ R\\HYK RYZa[ R\\D\\J',
	    'PoZHkH R\\KiK R[PjP RYSlS R\\[l[ R[M[P R\\E\\J RcDcP R\\EjEjJ R[MjMjP RTDVG RRMWMWWR[ RaQ]UkX RhSdW[Y RVX[Za[',
	    'Po[HkH R]KaK RfKkK R]MaM RfMkM R^RjR R\\UlU R[[l[ RdJdN RdOdY R[DkDkH RRPVPVWR[ R_O]T RSDWF RRJWL RYZa[ R[D[OYV',
	    'PoYFlF R[RjR RYWlW R[[l[ R_C_H RbIbW RfCfH R]LaP RSDWH RhLkP RhJfO R]J[O RRNVNVXRZ RVY[Za[',
	    'PoZHkH RcOkO RaUkU R\\[l[ RbCbM RcOcU RgMgY R\\D^F RRPWPWWR[ R[Q_TZX RhDeF RTDXF RSJWL RdIlL R\\MZS R\\PaO^V RbG`IYL RYZa[',
	    'PoZFlF ReLjL R[NjN R[QjQ RYTlT R[I[Q ReFeL R[IjIjQ RSDWH R^U`W R^D`E RRNVNVWR[ RfCeF RgQfXcX RUX_[l[ RaFaJ]L',
	    'Po[EjE RZIkI RZLaL RdLkL RcOlO RYRlR R[[l[ R^J^O RbCbH RgJgO ReReXkWkX RRPVPVWR[ R_T[X RSDXF RRJWL R_R]W RaOZP RYZa[',
	    'PoYElE RZKjK RYNlN R]TfT RZHZK ReEeK R\\QfQfT RZHjHjK RSDWH RRNVNVWR[ R`E`MZR RfNiRlS R]T]XiXiV RUX_[l[',
	    'PoZHaH RdHjH RZKaK RdKlK RZOkO RYTlT R_M_T RZEaEaH RfMfT RdEjEjH RSDWH RRNVNVWR[ RZHZL RfVkX R_VZX RdHdL RUX_[l[',
	    'Po`LcL RdQjQ R_VeV RgXjX R[[l[ RZNZX R_S_W RcGcL RdNdQ R_SeSeV RZNjNjX RTDWG RSJVL RRPVPVWR[ RYHjHgJ R_FfH R\\DhDcGYL R`N_Q\\Q RVX[Za[',
	    'Po[HiH RYKlK R\\PiP R\\RiR R\\UiU R[E[H R\\M\\U RbCbK R\\MiMiU R[EiEiH RSDWH RRNVNVWR[ ReVkY R`VZX RUX_[l[',
	    'Po[IaI RdIkI RZMlM R\\SiS R[[l[ R[D[I R\\O\\S R[DaDaI RdDdI RiOiS RdDkDkI RSEWH R^J`L RUXR[ RgJeM RcMcT^X RRMVMWX[Za[',
	    'PoYFlF R]NhN R]RhR R_YbY R\\[l[ R]J]R RbRbX R\\LhKhR RSDWH RZH]K RgTjW ReHjM RRNVNVWR[ R]TZW RjHgK R`GZM RaC^J RYZa[',
	    'PobEjE R_JfJ RbMhM R]OkO R[[l[ RbCbL RfRkW RRPVPVWR[ RjRgS ReOgT RSDWF RRJWL R[HkHkJ RdO]R RcR]U RcQeU]W ReUdXaX RVXZZa[ R[H[PYW',
	    'Po\\GcG ReGlG RZKkK RZWlW R[[l[ RcKcW RSDWH R^PaT RhPlT R^F_I RfFiI RRNWNWXRZ RhMeT R]DZI R^N[T RfDcI RVY[Za[',
	    'PobGlG RZK`K RbMlM RcRkR R[VaV RZE[P[X RZE`E`K R[OaOaV RdHeL RgMgX RSDWH RRNVNVWR[ RjHiL RgCgG RZJYU RUX_[l[',
	    'PoZIaI RZLaL RXObO R[[l[ RZFZL RZFaFaL RhTlX RRPVPVWR[ RgSbX RfNjW RSDWF RSJWK RiMeV R]C]F R`X]X RdIhT RdIkHgT R]L]OZW ReCbN RVXZZa[ R\\RaR`X',
	    'PoZElE R\\KiK R\\NiN R]UhU RgXjX R[[l[ R[Q[X R\\H\\N R_C_G RbHbU RfCfG R\\HiHiN R[QkQkX RfShU RRPVPVWR[ RSDWF RRJWL RYZa[',
	    'PoZMaM RdOkO RZQaQ RcSlS R^XaX R[[l[ R^H^Q RYJaIaX RhLhX RSDVG RSJVL RRPVPUXR[ R[F_F]K RfEdJ ReMcQ RcDkEjJhJ RUXZZ`[ R[CYIZRXX',
	    'PodJiJ RYMlM R[[l[ RbCbF RdFdJ ReReW RZFZGkFkH R_K`M RRPVPVWR[ RfKdM RSDXF RRJWL RaNdRjU R`F_IZK RcMZQ RcQ[T RiPdR RcTZX RdX`X RYZa[',
	    'Po[HjH RYKlK R\\QiQ R[D[H R\\M\\Q R`D`H RbQbY ReDeH R\\MiMiQ R[DjDjH RSDWH RRNVNVWR[ RjRgU RbQdUkX RaSZW RVX`[m[',
	    'PoYElE R_LeL R[NiN R]ShS R]VhV RZXkX R[[l[ R[H[N R]P]U R_J_L RbCbE R_JeJeL R]PhPhU R[HiHiN RSDVG RSJVL RRPVPVWR[ RVXZZ`[',
	    'Po^KfK R\\RhR RZWjW R\\[l[ R\\N\\R R\\NhNhR RSEWH ReGkM RhEeH RjGgJ R[G^HYM RVWR[ R^T^V RcCfI RfTdW RZE`E^I RROVOVWXY[Za[',
	    'PoZElE R[[l[ R[I[X RbEbX R[JjJjX R^M`O RRPVPVWR[ R]R`S\\U ReMgNdO ReRgTdU R]FZJ R`L\\O RSDWF RRJWL RfGkI RfXjXfX RYZa[',
	    'PobJkJ RhXkX R[[l[ R`O`V RfCeG RbFbOjOhX RbFkFkN RSDVG RRJVK RYH[J RRPVPUXR[ R]OYQ RlUkX R]F_H^KXM R_P\\TYU RbW`X R_DXF R_X\\X R_L`S RdOcW RXZ`[',
	    'Po\\HiH R\\JiJ R\\LiL R]XhX R[[l[ R\\E\\L R]T]X R]ThTiX R\\EiEiL RaRgQjT RRPVPVWR[ R]QZU RSDXF RRJWL RZOkOkP RYZa[',
	    'Po\\GiG R\\IiI R\\LiL RdQiQ RZSlS RcYgY R[[l[ R\\E\\K RbLcNdQ R\\EiEiK RZNkNkP RRPVPVWR[ RSDXF RRJWL R_VhUgY R_O[Q R`T]WZX RYZa[',
	    'Po[MjM R[[l[ R[I[M R[O[W RbObW R[IjIjM R[F]H RaFcH RgFiH RRPVPVWR[ R]C[E RcCaE RiCgE RSDXF RdPgQ RRJWL RdTgT R]P`Q R]T`T RlVlX R^JgL RfJ]L R`VYW RgVaW RiPjX RYZa[',
	    'PoYGaG RgLjL R_XbX R[[l[ R^D^L RbObX RTDWG R]Q_S RSIVL RRPVPVWR[ RgQeS R]GXL R]HaJ RbLlQ RcTjV ReCbI RgFeK RaMYQ RaTZV RdFkFjL RYZa[ RaDYD',
	    'Po]EhE R[MiM R\\QhQ RYSkS R[T_U R_YcY R\\[l[ RcScY R\\OhOhS RTDWH RRMWMWWR[ RcUjX R`CZF RfKbK R[IiIiLfK R[I[L^K[K RjUdVZW R_FkH RVX[Za[ RfFYH R_KbJbL_K',
	    'PoYL`L RXNbN RbPkP RZP`P RZR`R RYXaX R[[l[ RYUZX RYUaUaX RgOgX RSDVG RRJVK RiMlP RRPVPUXR[ RiHgL RbIgLdO R[FaF\\J RfGdJ RjRkV R[F^H RdRbW R\\CYH]IXK RjDbE RXZ`[',
	    'PoZHkH RcMlM RcPkP RXR`R RcTkT RcWlW R[[l[ RZDZH R]Q]Y RdKdY RhKhW RSDVG R^OaR RYL\\N RSJVL RRPVPUXR[ R\\JZL R_LYQ R_S`V RYSYW RdIaO R_D_H ReDeH RZDkDjH RUXZZ`[',
	    'PoYDaD RcDlD RZLlL RZQkQ R]TbT R[[l[ RZFZJ R]Q]X R_L_Q RZF`F`J RdFdJ RdFkFkJ RZNkNkQ RSDVG RSJVL RRPVPUXR[ RjTdT RbW[X RUXZZ`[ ReLeXkXkY RZLZRXX',
	    'PoTJhJ RRSjS RS[k[ RSMSZ RTETJ R^M^S RTEgEgJ RRMjMjT RlXk[',
	    'PoRE_E RUZZZ RZEZZ RbEb[ RhIgMkQlV RbElEiK RkWfW',
	    'PofWkW RbEb[ RlRlV RTKYQ RXSRY R[T]W RgMkPlT RbElEhM R\\JWU RSF]FZR',
	    'PoSM]M RSMSY RRE]E]M RbEb[ RlRlV RgMkQlT RbElEhM R]VRY RkWfW',
	    'PoUNgN RSVjV RT[k[ RTQTZ R^Q^V R[F`H RRFWH RdFiI RiDdF RWDRF R_D[F RSQjQiV RUJUN RUJgJgN',
	    'PoRHRW RbEb[ RlRlV RgMlR RiIgM RbElEiK RkWfW RXCXVRW R]H]WVW',
	    'PoSE`E RRN`N RYEY[ RcEc[ RcElEhMkQlTkWgW',
	    'PoSE`E RRN`N RT[Y[ RYEY[ RbEb[ RbElEgMkQlV RkWfW',
	    'PoRI^I RRW]W RSISW RXCXI RaEa[ RiIgMkQlU RaElEiJ RkWfW',
	    'PoRI^I RbEb[ RlRlV RZU^X RZRSY RgNkPlT RTR\\V RbElEhN R]KYU RYCTR RkWfW',
	    'PoRF^F RXFXT RbEb[ R^SRU RbElEiJgMlSkWfW',
	    'PobEb[ R[TT[ RVN\\Q RWFSP R^MYW RXCUM RWH_H[T RbElEgMkQlTkWfW',
	    'PoUG`G RcEc[ RYD\\F RUGUTSZ RcElEhMkPlSkWfW',
	    'PoSI_I RRP`P RcEc[ RYCYR]V_X RgMlR RWVR[ RjGgM RcElEhL RlSkWfW RYKXU',
	    'PoSH_H RVL\\L\\X RcEc[ RlRlV RXCYF RgMkQlT RjHgM RcElEhL R`V[X RkWfW RVLVTUY',
	    'PoZ[^[ RdDd[ RWWS[ RXE]DaL RhMjPlT RdElEhM RVFSM RkWgW RYNWW RTN_N_U^[',
	    'PoSHaH RX[][ RZCZH RbEb[ RlRlV RgMlR RbElEhM RWKVW RXHXO_N_U^[ RkWfW',
	    'PoSEaE RRNaN R]E][ RdEd[ RlRlV RgMkQlT RdElEhN RkWfW RWEVRUX',
	    'PoRK_K RRR^R RY[][ RRE_E_Y RcEc[ RcElEhL RgMkPlSkWfW RXEXMWSS[',
	    'PoULaL R\\L\\[ RdEd[ RlQlV RgMkPlT RdElEhM R`DUFURSZ RkWfW',
	    'PoZCZX RcEc[ RlRlV RWVRZ RgMkPlT RcElEhM RXQUY RaTYX RRHRL RRH`H`L RkWfW RYIWU',
	    'PoSG`G RSM_M RRS`S RcEc[ RgMlR RXVS[ RiIgM RcElEiJ RlSkWfW RYCYPXV',
	    'PoTE_E RRMaM R\\M\\Y RcEc[ RlRlV RgMkPlT RcElEhM RaW[Y RWMUX RkWfW',
	    'PoSR_R RSKST RYCYY R_K_S RbEb[ RlRlV RgMkPlT RbElEhM RkWgW R`XVY R_ERG',
	    'PobEb[ RYCYQ`X RWVR[ RgMkQlV RbElEhK RYQUY R^I\\N RTISP RkWfW RYKXU',
	    'PoSE`E RRNaN RX[][ RVEVN R]E][ RcEc[ R]NZQTX RgMkQlU RcElEhL RkWfW',
	    'PoRG\\G R^JiJ RSM[M RRS[S R]SjS R^ZkZ R^M^Z R^D^J RcMcS R^DiDjJ R]MjMjT RlWkZ RWDWRVX',
	    'PoZM^M RZZ^Z RcEc[ RlRlV RgMlR RYCYF RcElEhL RTF^F^M RTFTP`P_U^Z R\\TRV RkWfW',
	    'PoUT\\T RY[^[ RULUV RUL\\L\\T RdEd[ RlRlV RgNkPlT RdFlEhM RWCRL RUH`G`T^[ RkWfW',
	    'PoRO_O RRX_X RRHRY RXCXX RRH_H_Y RdEd[ RdElEhMjPlSkWfW',
	    'PoRHaH RUP^P ReWkW RUX^X RUCU[ R^C^Z RdEd[ RlRlV RgMlR RiIgM RdElEiK',
	    'PoTM`M RRM[M RTZ_Z RTQT[ RTQ_Q_[ RcEc[ RlRlV R\\H`L RgMkPlT RcElEhL RWCTM RkWfW',
	    'PoUL`L RUFUX R\\L\\W RbEb[ RlRlV RgMlR RbElEhL R^DTF R`VRY RkWfW',
	    'PoX[_[ RXHXZ R_W_[ RdEd[ RXD\\G R[PRY RdElEhM R`E[Q R`LbT RTMST RjOlTlV RkWgW',
	    'PoSEaE RRZbZ RZHZX RcEc[ RlRlV R\\JaP RgMlR RZIRQ RcElEgM R[EYK RkWfW',
	    'PoRE`E R\\[`[ RSJS[ RSJ`J`[ RcDc[ RYEYM]T RcElEhM RXMVT RkWgW RYH[Q RjNlSkV RYGXQ',
	    'PoZN^N RTY_Y RTQT[ RTQ_Q_[ RcEc[ RWJSN RgMkQlU RcElEhL RXFVM RkWfW RSE_E^N',
	    'PoRKXK R\\ZaZ RXDX[ R]D]Z RaWaZ RdEd[ RaH\\M RjNlT RdElEhM RXTRW RkWgW',
	    'PoRE`E R][a[ RVEVN RUK]K^Z RaWaZ RcEc[ RXOYT RZYRZ RkWfW RcElEgMkPlTlV RVGUMUPUTTZ',
	    'PoTL`L RSZ^Z RTETV R`T`W RcEc[ RlQlV RgMkPlT RcElEhL R[URV R\\D[K]V R_DTE RkWfW',
	    'PoUS_S RTY_Y RTM`M`[ RdEd[ RhMkPlT RdElEhM RWCTL RWF^F[M RkWfW',
	    'PoXCXY R]D]X ReEe[ RlQlV RjNlS ReElEhM RkWgW RSJUS RaI`R RbVRZ',
	    'PodEd[ RYJ[M RWU[[ R[FaK RTP_P]TYX RZERL RjNlT RdElEhM RkWgW',
	    'PoRE_E RUI^I RVO]O RTU^U RRZ^Z RRERZ RYIYU RcEc[ RlRlV RgMlR RcElEiK RkWfW',
	    'PoSG_G RRM`M RSR_R RYCYM RYNYX RcEc[ RgMlR RiIgM RcElEiJ RlSkWfW R`WRY',
	    'PoUIaI RSOaO R[C[[ RdEd[ RYOaV RYRRX RgMkPlT RdElEhM RWDSL RZMXS RkWgW',
	    'PoTG_G RRLaL RSQaQ RY[][ RZCZL R]N][ ReEe[ RVTYW RjNlT ReElEhM RkWfW',
	    'PoSGaG RVL^L RSPaP RY[][ RdEd[ RlRlV R[GaL RYFSL RgMkPlT RdElEhM RZCWI RWPUT_T]Z RkWfW',
	    'PoSGaG RVP`P RVT`T R\\[_[ RWJW[ RVL`K`Z RdEd[ RlRlV RWKRO RgMkPlT RdElEhM RYCUN RkWgW',
	    'PoRGaG RSN\\N RdEd[ R[U`Z RXCZGSM RjNlT RdElEhM R^KWRRT R_PXXRZ RkWgW',
	    'PoUL^L RUY_Y RUQUZ RUQ_Q_Z RcEc[ RlRlV R[EaL RXFRL RgMkPlT RcElEgM RYCWH RkWfW',
	    'PoRNbN RVZ]Z ReEe[ RTEaJ ReElEhM RWQRZ R_C[HSK RXLTX R[QXZ RiNlSkWgW R_UaZZZ',
	    'PoREaE RUM`M RRM]M RSR`R RcEc[ R\\I`M RcElEhL RXEUM RgMlRkWfW RZNZYTZ RaXYY',
	    'PoRG`G RSM_M RTZ^Z RTQT[ RYCYM RTQ^Q^[ RcEc[ RlRlV RgMkPlT RcElEhM RkWfW',
	    'PoUO\\O RUT\\T RZZ_Z RUKUU RUK\\K\\T RdEd[ RdElEhM RWCSJ RjNlSkWgW RUGaG`S_Z',
	    'PoTJaJ RWX`X RWPWZ RWP`P`Z RcEc[ RlRlV RhNkPlT RcElEhM RkWgW R`DTFTSSZ',
	    'PoSEYE R[EaE RZObO R^E^[ RdEd[ RZMRO RVEVQUX RdElEhMkPlSkVgW',
	    'PoRGaG RcEc[ RVP^X R]J`M RWJRN RgNkQlW RcElEgN RZCZG RkWgX R\\OZTTZ',
	    'PoRM^M RRS^S ReWkW RRY^Y RRGR[ RRG^G^Z RcEc[ RlRlV RgMlR RjHgM RcElEhL RXCVG',
	    'PoSG]G RaJjJ RRM^M RSS]S R`TkT R`[k[ RXCXM RXOXY R`M`[ RaDaJ ReMeT RaDjDjJ R`MkMkU RlXk[ R^XRZ',
	    'PoRO^O RZ[^[ RUG^G^[ RcEc[ RYIZM RYR[U RgMkPlT RcElEgM RYCYG RUGUTSZ RkWfW',
	    'PoTL^L RTQ^Q RTGTY RTG^G^Q RbEb[ RlRlV RZS^W_Y RgMlR RjHgM RbElEhL RYCYG R]WRY RkWfW',
	    'PoSHaH RSPaP RdEd[ RZCZRaY RXUS[ RUJVN RhMkPlT RdElEhM RZQWX R_J^N RkWfW RZLYU',
	    'PoVL_L RRQbQ RdEd[ RlQlV RZD]FaK RZERL R_Va[ RhMkPlT RdElEhM RXQTZ R[YSZ R_XWY RkWfW',
	    'PoSIaI RRPaP RcEc[ RZIZQ`Y RVDWG RXUR[ RgMkPlT RcElEhM R^C\\I RZPVX RkWgW RZKYU',
	    'Po`JjJ RTK\\K RTP\\P R_TkT R`[k[ RTGTY RXCXG RTG\\G\\P R`M`[ R`D`J ReMeT R`DjDjJ R_MkMkU RWS\\X R\\RYU RlWk[ R[WRY',
	    'PoRJ`J RSQ`Q RRDRH RZMZ[ RRD`D`H RcDc[ RhMkPlT RcElEhM RXGSQ RZURV R]UVV R`UYV RkWfW',
	    'Po_LbL RSMXM R[UcU RgWkW R[ZcZ R[K[U R_E_U ReDe[ RjNlS ReElEhM RSDXDTL RWRTY RXNUX RbDZE RTQVVYY`Y',
	    'PoTHcH ReEe[ R_DaF R^VYZ ReElEhM RbWb[ RaM\\X RTMZMZVVV RkWgW R]C]L]O^Y RjOlTlW RTHTSSZ',
	    'PoRE_E RSO]O RSISO RSI]I]O RcEc[ RgMkQlU RcElEhL R[Q[X RURUV R_WRY RkWfW',
	    'PoSMbM RVPVY R[O[[ RcEc[ RTRRT RjNlT RVD`I RcElEhM RkWgW R^DSJ RXJTR`Q`Y]Y',
	    'PoRM^M RRZ^Z RRQR[ RRERL RRE^E^L RRQ^Q^[ RcEc[ RlRlV RgMlR RiIgM RcElEiK RXMWR RkWfW',
	    'PoRGbG RSPaP RSUaU R^[a[ RSLS[ RZCZ[ RSLaLa[ ReEe[ R]DaE RiNlT ReElEiN RkWgW',
	    'PoSDaD RRObO RTZ_Z RTST[ RSI_I_O RTS`S`[ RdDd[ RdElEhMkPlSlVgW RXEVO',
	    'PoU[Y[ RZQZ[ RYFYJ RcEc[ RgMlR R_FaJ RcElEhL RUERK RTM_M]OYP RlSkWfW R_DUE RaRZSTT R^SWT',
	    'PoUHaH RRMbM RUY`Y RURU[ RZCZP RUR`R`[ RdDd[ RjOlT RUDSK RdElEhMlRlVgW',
	    'PoTG`G RRLaL RY[\\[ RZCZL R\\L\\[ RdEd[ R_PaU RTPSU RdElEhMkPlSkWgW RXNVY',
	    'PoTK^K RTP^P RTGTX RTG^G^O RbEb[ RlRlV RYS_Y RgMlR RjHgN RbElEhL RYCYG R_R\\VRX RkWfW',
	    'PoRGbG RdEd[ RlRlW RULWP RZCZRaZ R_MbP RYUS[ RhNkPlT RdElEhM R_K\\Q RkWgW RUJTQ RZLYV',
	    'PoSEaE RTM`M RTR`R RTITR RTI`I`R ReEe[ RlQlV RjNlT RTT`Z ReElEhM RYVVYR[ RkWgW RZEZQYV',
	    'PoSIaI RTN_N RVY_Y RWPW[ RTD_D_N RUR_R_Y RcDc[ RVQRU RYDXJVS RcElEgMkPlSkWfW',
	    'PoTK_K RRS`S RTETK RTE_E_K RcEc[ RlRlV RgMlR RiIgM RcElEiK R^MSO RYNZYRZ R^XUZ RkWfW',
	    'PoRFaF RU[Y[ RRKRN RYCYK RYQY[ RRK`K`N RcEc[ RgMjPlT RcElEhM RUO^OYQ R\\SST R`SWT RkWfW',
	    'PoUY^Y RUQU[ RUQ^Q^Z RcDc[ RlQlV RZJ_O RZD_H RYDTI RZJRQ RgMkPlT RcElEgM RkWfW',
	    'PoTJ^J RTO^O RTU^U RTETU RTE^E^U RcEc[ R[W_Z RVWR[ RcElEgMkQlTkWgW',
	    'PoREbE R_[a[ RSIS[ R\\I\\[ R\\IbIb[ ReDe[ RUOWS R]O_S ReElEhM RkWgW RjNlSkW RSIYIY[V[',
	    'PoUI_I RSMSV RUEUI RUE`E`I RSM`M`V RdEd[ R]X`[ RdElEhM RZOZVWYR[ RkWgW RjNlSlV',
	    'PoSG`G RRNaN RTY^Y RTRT[ RYCYG RTR^R_[ RcEc[ RUIVL R^H\\M RcElEgMkPlSlWgW',
	    'PoRLbL RWS`S RWW`W R^[`[ RWOW[ RVQaPa[ ReEe[ RWORT RVDaI ReElEhM RYJUR R`DTJ RkWgW RjNlSlV',
	    'PoRFaF RRLbL RSP`P RRSbS RSI`I`P RdEd[ RdElEhM RYNWT RYCYPUWa[ R^U[YS[ RjOlTlW RkWgW',
	    'PoSKaK RSPaP RRUbU RSFSP R]Q][ RSFaFaP RdEd[ RhNkPlT RdElEhM RYCYFZOWRSS RkWgW',
	    'PoSFaF RTIaI RRLbL RUR`R RUV`V R\\[`[ RUOU[ RZCZL RUO`O`[ RdEd[ RdFlFhNkPlSlVgW',
	    'PoRFaF RTN^N RRV`V RV[Y[ RTITN RYCYF RYTY[ RTI^I^N RcEc[ RlRlV RgMkPlT RcElEhM RTQ_QYS RkWgW',
	    'PoWI^I RVM^M RWV]V R\\[a[ RWQWX RZFZM RWQ]Q]V RTEaEa[ RdEd[ RhMkPlT RdElEhM RkWgW RTETTR[',
	    'PodDd[ RlQlV RaQ^T R`F^I RhMkPlT RYJ`M RYV_X RdElEhM RUFTI RUQTU RYJWMSN RYUVYR[ RZCYK RZNYW RkWfW',
	    'PoREcE R\\JbJ RTJZJ RTPZP RTETW RZEZ[ ReEe[ R^ObX ReElEhM RaP]Y RkWgW RbJ_V RjNlSkW RZVRW',
	    'PoRI`I RRPaP R\\I\\[ RcEc[ RUDVH RhMkPlT RcElEhM R^D[I RVIVTSZ RkWfW RVNUW',
	    'PoSGbG R[C[[ RdEd[ RZPbW R_LcP RYRRY RVISQ RVLZOXT R_I\\P RkWgW RdElEhMjOlTlW',
	    'PoTJYJ R[K`K RTP`P RUEUP R\\P\\Z R[E`E`P RcEc[ RhMkPlT RcElEhM RUZR[ RaW[Y RkWgW RYCTE RWPVX',
	    'PoSIZI R\\IcI RWCW[ R_C_[ ReDe[ RVJ[O R_KcO RhMkPlU ReElEhM R^MZV RWHSU R_H[T RkWgW',
	    'PoSIaI RRNaN RSSaS RUIUS RZEZY R^I^S RcEc[ RiMlW RcElEhM RkWgW R`DUE R`XTZ',
	    'PoRF]F RaJjJ RRN^N R`TkT RTY\\Y R`[k[ RTRT[ RXCXG RTR\\R\\Z R`M`[ RaDaJ ReMeT RaDjDjJ R`MkMkU RlWk[ RUHVM R[H[M',
	    'Po\\JbJ R\\ObO R\\D\\Y R\\DbDbO ReDe[ RXVS[ RWCSIVMTR RYIUP RYQVY ReElEiMlRlVgW RZNWW RYQRS R`RcXZY',
	    'PoTMaM RTRaR RRVbV RTITR RZIZ[ RTIaIaR ReEe[ RVDWG ReElEhM R^C\\I RkWgW RjNlSlV',
	    'PoTH_H RTL_L RTO_O RRTaT RTDTO RTD_D_O RcDc[ R]Q^R R\\W_Z RXUSZ RhMkPlS RXU]W RcElEhM RYQWW RkVfW',
	    'PoVG\\G R`OkO R_UkU R`[l[ R`Q`Z ReQeU R`KkKkO R_QkQlU RkXl[ RVIYJ RVTZV R_FaI RdFgI RiFlI RkDiF RXDSJ RaD_F RXORU RfDdF R[HVMRO RWQ]Q[UWYR[ R`K`O',
	    'PoVG_G RVJ_J RVM_M RSO`O RfWkW RSESO RVDVL RVD_D_L RcDc[ RRVTZ RTRRV RZRXVZ[ R]V_[ RgMkPlS RcElEhM R_R]V',
	    'PoRIbI RTP`P RRS`S RSWaW RTSSW RTLTP RZPZ[ RTL`L`P RcEc[ RcElEhM RXCWJ RTF_F_J RgMkQlTkWfW',
	    'PoTG]G RRLaL RWU_U RWY_Y RWPW[ RYCYL RWP_P_[ RcEc[ RgMkPlW RcElEhM R`D_HWPRR RlWfW',
	    'PoREbE RWJ`J RWM`M RUQbQ RTZbZ RTETY RWGWM RWG`G`M RdEd[ RlQlV RjNlT RXS`W RdElEhM RZNWT RkWgW R^Q]T[VVX',
	    'PoTJaJ RWQaQ RWVaV RWZaZ RWMW[ R[E[J RWMaMa[ RTEaEaJ RdEd[ RjNlT RdElEhM RkWgW RTETRSZ',
	    'PoRFaF RRMbM RVY`Y RWQW[ RWCWJ R]C]J RUS`R`Z RdEd[ RVRRV RYJTT RdElEgMkQlTkWgW',
	    'PoRGWG RTR`R RTV`V R\\[`[ RTOT[ RTO`O`[ RdDd[ R\\D\\LaKaL RaF\\H RWDWLRK RdElEhMjOlSkVgW',
	    'PoSJXJ R[J`J RTM_M RRQaQ RX[][ RSESJ RSEXEXJ R[E[J R[E`E`J RcEc[ RcElEhM RkWgW RVQUU_U][ RjNlSkW',
	    'PoVJ_J RTQYQ RTUYU RW[Y[ R^[a[ RTMT[ RTMYMZ[ R]N]W RaMa[ ReEe[ RjNlT R^GcI RZEaH ReElEhM RkWgW R[CVHRJ',
	    'PoREaE R\\LaL RSPaP RST`T RSHSO RZQZY R\\E\\L RSHaHaO RdDd[ RXEXKUM RjNlT RdElEhM RkWgW R]YSZ R`XVY',
	    'PoSM`M RWV^V RTW\\W RRQR[ RSHSM RREYEYV RSH`H`M RdEd[ RlQlV R\\S^W RhMjPlT RdElEhM RRQ`Q`[][ R_CWD RkWfW',
	    'PoSI`I RSP`P RST`T RRWbW RSDRG RSLST RZFZ[ RSL`L`T RRDaDaG RdEd[ RdElEhM RjNlSlVgW',
	    'PoXJbJ RYObO RXTcT RVHV[ RZE`E`J ReEe[ R]UbY R\\WX[ ReElEhM RWDRN R[KXQ RiNlSkWgW R^N]W',
	    'PoVM_M RSRaR RdDd[ RZMZT_Y R^IbN RYVT[ RTGWJSN R`D]G RaH_J R\\D_L RhMkPlS RdElEhM RTDYEVL RkWgW RZOYV',
	    'Po[KbK R[QbQ R[F[Y R[FbFbQ ReEe[ R]D`F RXVR[ RVNRR ReElEhM RVDRL RYHVN RYQVX RiNlRlV RYOWW R`ScYZY RkWgW RYQRR RXKRK',
	    'Po\\JbJ R\\PbP R\\D\\V RZDcDb[ RfDf[ RVMRQ RWVRZ RjMlS RVCSL RXIVN RYOUX RfElEiM RlVhV RXKRL RXQRR RcUZW',
	    'PoRJWJ R\\JaJ RRNaN RSFSN RZCZR R\\FaFaN RdEd[ RjNlT RUT`Y RdElEhM RRS`S\\WS[ RWERF RkWfW',
	    'PoTFaF RTMbM RVQ`Q RVZ`Z RVTV[ R[G[T RVJ`J`Q RVT`T`[ RdEd[ RZC\\F RhNjPlT RdElEhM RkWgW RTFTSR[',
	    'PoUM_M RUP_P RRTbT RRERH RUIUP RZQZT RUI_I_P RREaEaH RdDd[ R]W`Z RWWR[ RdElEhMkPlRlVgW',
	    'PoTI`I RTM`M RTQ`Q RWZ^Z RTETQ RWTWZ RYCYF R^W^Z RTE`E`Q RdEd[ RlRlV RYR\\U R`TbY RgMkPlT RdElEhM RTTRY RkWfW',
	    'PoTDbD RXHaH RTKbK RVTbT R\\[_[ RXKXQ R_R_[ ReEe[ RXVZX RaM_O RjNlS R[MbP ReElEhM R\\QWQ RkWgW RTDTRS[',
	    'PoRFbF RROaO RTV`V RTZ`Z RTRT[ RZRZZ RTR`R`[ RdEd[ RlRlV R]LaP RZFUJ R]HWO RgMkPlT RTJYL RdElEhM RZCYG RkWfW',
	    'PoSEaE RRNbN RTV`V RTZ`Z RTQT[ RXEXN R\\E\\N RTQ`Q`[ RdEd[ RSGUK RjNlT RdElEhM R`G_L RkWgW',
	    'PoUK]K R\\N`N RUW]W R[[_[ RUSUX RUGUK RYSYX R]T]W R]H]K RdEd[ RVERI RVPRT RjOlT RdElEhM RXLSN RXYS[ RYGXL RkWgW RVFaF`N RURaR`[',
	    'PoTI_I RTL_L RTO_O RRTaT RTFTO RTF_F_O RcDc[ R]Q^R R\\W_Z RXUSZ RYCXF RhMkPlS RXU]W RcElEhM RYQXW RkVfW',
	    'PoTL_L RTObO R[[_[ RWUWY RTF_F_K RdEd[ R\\T^V RYCYF RZU[X RdElEhM RTVRZ RjNlSkWgW RTFTSaS`[',
	    'Po\\KcK R\\OcO R\\ScS R\\WcW R\\G\\[ R_C]GcGcO ReDe[ RXVS[ RWCSIVMTR RXIUP RYQVX RkWgW RjNkQlU ReElEiM RZNWW RYQRS',
	    'PoUI_I RTO`O RTS`S RTV`V RTLTV RUDUI RUD_D_I RTL`L`V RdDd[ R]XaZ RdElEhL RWXRZ RgMkPlSkVfW',
	    'PoRFaF RUM^M RVW]W R][`[ RSPS[ RUIUM RVSVY RZCYF RVS]S]W RUI^I^M RSP`P`[ RcEc[ RlRlW RhNkPlT RcElEhM RkWgW',
	    'PoTFcF RVKcK RWNaN RVTaT RVWaW R_[a[ RVQV[ R\\C[F R\\G\\[ RWIaIaN RVQaQb[ ReEe[ ReElEhM RTFTRRZ RjOlSkWgW',
	    'PoTJ_J RRMaM RWW\\W RTZ`Z RTPT[ RTETI RWSWW RYJZM RWS\\S\\W RTE_E_I RTP`P`[ RcEc[ RlRlV RgMlR RcElEhL RkWfW',
	    'PoRFaF RTN`N RTQ`Q RRUbU RTJTQ RWCWH R]C]H RTJ`J`Q RdDd[ R[V`[ RYU]X RhMkPlS RdElEhM RYWWYR[ RZQXX RkVgW',
	    'PoTM`M RTQ`Q RSTaT RTJTP RZJZ[ RTJ`J`P RdDd[ RTFVH RXF[H R]F`I RVCSF R[CXF R_C]F RXT`W RZTVXRY RdElEhMkPlSkWgW',
	    'PoTFbF RTObO RWT[T RWQWZ RYGYO R[C[F R]G]O RTJbJbO R^Q][bYb[ ReEe[ ReElEhN RbS]U R[YUZ RjOlTkWgX RTFTTSZ',
	    'PoSDXDXL R[D`D`L ReEe[ RTFVH R[G^G RZNbQ ReElEhM R`H[K RZNRQ R[QUT R]TUW RkVgW RXHSK R_WYZT[ RkOlU',
	    'PoRFaF RUI_I RSP`P RSSaS RSV`V RZZ`Z RVZ\\Z RSLSP RUCUI R_C_I RSL`L`P RdEd[ RdElEhM RjNlSlVgW RZIZZSZ',
	    'PoSFbF RTLaL RRRaR RRUbU RZ[^[ RZCZR R^S^[ RTIaIaO ReEe[ RTITO`ObS RVVXY RhMkPlS ReElEhM RkWgW',
	    'PoSDbD RUJXJ R]J`J RTMXM R]MaM RTPaP RRScS RZ[_[ RSGSJ RZDZN RSGbGbJ ReEe[ ReElEhM RkWgW RVSUV`V_[ RjNlSkW',
	    'PoTH_H RTJ_J RTMbM RTS_S R^VaV RZQZ[ RTE_E_J RTETObObU ReEe[ R\\V_Y RYS\\V RZSSZ RYCXE ReElEhM RjNlSkWgW',
	    'PoSEbE RZHaH RRLbL RUPbP R\\[`[ RWVXZ RZEZL RdEd[ R]U_W R[V[Y RdElEhM RSVRZ RjNlSkWgW RUIVMTTbT`[',
	    'PoSFaF RRJaJ RTP`P RTS`S RRWbW RTMTS RVGWJ RZCZF RZSZ[ RTM`M`S RcEc[ RlRlV RgMkQlT RcElEhL R^F]J RkWfW',
	    'PoTG_G RTJ_J RSQaQ RgWkW RSMSQ RTDTJ RWMXQ R\\M\\Q RTD_D_J RSMaMaQ RdEd[ RlSkW RjNlT RdElEhM RUV`Y RST`T]WXYR[',
	    'PoTI_I RTM_M RTPbP R[[_[ RWUWY RTF_F_L RdEd[ R\\T^V RYCYF RZU[X RdElEhM RTVRZ RkWgW RjNlTlV RTFTSaS`[',
	    'PoSMbM RRSbS RVEVS RYFYS RXUXZ R\\F\\S RUGaF`S ReEe[ R\\U\\Z R`UbY RjNlT ReElEhM RVCSI RTUSZ RkWgW',
	    'PoVS_S RVNVS RVN_N_S RdDd[ RXK^JbM RTGWIRN R_D]G RbH^I RWUXX R\\D_K RTEYEVK R^U\\X R\\YS[ R_YWZ RdElEhMjNlSkWgW RbYYZ',
	    'PobGlG RURgR RSXiX RT[k[ RRGRM RTTT[ RUOUR RXCXM RRG]G]M R^T^X RUOgOgR RSTiTiX RSDTE R]D[E RZI[L RbGeJlM RUITL RcCaH RjFfJ`M',
	    'PoRHbH RTK`K RSNYN R[RaR RRUbU RTD`D`K R[MaMaQ ReEe[ RVVXY RkWgW RVNVR R[M[R R^S^[Z[ RYQRR ReElEhMkPlSlW',
	    'PoSIaI RSL`LaM RTR`R RTT`T RTW`W RSHSK RTNTW R\\C\\M RRO`O`W RSEaEaH ReEe[ RXCXKTN R\\YbZ ReElEhM RWYRZ RkPlU RkWgW',
	    'PoRN`N RSU_U RSY_Y RRGRN RSQS[ RYGYN RSQ_Q_[ RRG`G`N RdDd[ RVCWF R]D[G RUIVL RgMjPlS RdElEhM R]I\\L RkVgW',
	    'PoTE\\E R][a[ RYUYY RdEd[ RkPlV RUFUI R\\U\\X R^T_W RZFZH RjNlT RdElEhM RVUUZ R_F^I RUJ^J^O RUN`N`S RkWgW RUJUQSY RUSbSaZ R_DXD',
	    'Po\\NaN RTPaP RTRaR RRUaU RTKTR RXGXM RVG]G\\M RTKaKaR RdEd[ RXE^DbH RVERI RZWaZ RdElEhM RXYS[ RZRYX RWNUN RjNlSkWgW',
	    'Po_KbK RUQaQ RUTaT RUWaW RTETK RUNUW R\\D\\M RUNaNaW R\\DbDbK ReEe[ RYJVM R]XbZ ReElEhM RXXR[ RkWgW RiNlRlV RYDTE RXJRJ RYFXL',
	    'PoSGbG RTJaJ RRMbM RRRbR RTZ`Z RTUT[ RZGZR RTU`U`[ RdEd[ RUNVQ RVCWF R^O]R R^C]G RjNlT RdElEhM RkWgW',
	    'PoSHbH R\\QcQ R\\WcW R[C[N R\\T[X R`O`[ RdDd[ RUDWF RXVT[ R`D^F RjNlT R[IaL RSTXV RdElEhM RVOST RURZRWX R[HWLRM RkWgW',
	    'PoRKbK RUU`U RUY`Y RUQU[ RSE[E[P RZQZY RUQ`Q`[ RdEd[ RUGVI RhMjPlT RYKaN RdElEhM R_F]J RkWgW RZJTQRQ R`DWE',
	    'PoSIXI R\\IaI RTP`P RTS`S RRWbW RSDSI RTLTS RSDXDYI RZLZ[ R\\D\\I RTL`L`S R\\DaDaI RdEd[ RjNlT RdElEhM RkWgW',
	    'PoRIWI R[I`I RRLWL R[QaQ RRQWQ RRVWV R[VaV RRLRW RRDRI R[D[Z RRD`D`I R[LaLaV RXDXZaYaZ RdEd[ RdElEhM RjNlRlV RkWgW',
	    'PoSHbH RSMbM RTQaQ RSTbT RYCYH RZMZ[ R\\C\\H RdEd[ RUDVG R`D_G RWJWM RYT\\V_X RdElEhM R^I]L RZTVXRZ RjNlSlVgW',
	    'PoVI_I RSPXP R\\PbP RRSbS RSKSP RVDVI RSKXKXP RZQZ[ R\\K\\P RVD_D_I R\\KbKbP RdDd[ RVWRX RZRUX RZUaX RdElEhMjOlSkWgW',
	    'PoVI_I RTQaQ RUW`W RUZ`Z RTLTQ RUTU[ R[L[Q RUT`T`[ RTLaLaQ ReEe[ RZD^EbI RWMWP R^M]P RZCUIRJ ReElEhMjPlSkWgW',
	    'PoSIaI RSLaL RRObO RTU`U RRZbZ RSESL RXCXL R\\C\\L RTQ`Q`U RSEaEaL RdEd[ RWWWY RdElEhM R]W]Y RTQTU RkWgW RjOlSkW',
	    'PoRFcF RTMaM RRPRS RTITM RXCXH RXIXN R]I]N R^C^H RTIaIaM RSPbPbS ReEe[ RXV[W ReElEhM RYSSV RWT_T\\WUZ RkWgW RiNlRlV',
	    'PoTHZH RTKZK RTDTN R[Q[[ ReEe[ RlQlV R\\TaZ R]HbN R`T]U RWPSS RaJ\\O RWURZ RjNlT ReElEhM RRD\\EbF`L R^PXR RkWgW RZDZNRN',
	    'PoTFbF RTHbH RYL_L RTNcN RWTaT RWWaW RWQWW RYFYL R[C[F R\\N\\W R_F_L RWQaQaW ReEe[ R_YbZ RjOlT ReElEhM RYYU[ RkWgW RTFTRSZ',
	    'PoSEYE R\\EaE RRHYH R[HbH RTNaN RTS`S RTV`V RRXbX RTQTV RZMZ[ RTQ`Q`V ReDe[ RVCVIXL R^HbL RVJRM ReElEhM RjNlSkWgW R^D^K',
	    'PoSFYF R[FbF RSIYI R[IbI RUQ`Q RUT`T RUW`W RUNUW RUN`N`W ReEe[ RVCVJXM R_JbN R^H_K R]XbZ ReElEhM RTMRN R\\L[M RXYR[ RkWgW RjOlRlV R^D]K',
	    'PoTEcE RWJaJ RWMaM RWOaO RVRbR R^UbU RWWbW RUZcZ RWHWM R[C[F R\\U\\Z R\\H]R^U RWHaHaM ReEe[ ReElEhM RYTVU RkWgW RjOlTlW RTETTR[',
	    'PoRDcD RTIaI RTLaL RWS^S RVV`V RRNRQ RYDYG RTGaGaN RTGSNcNcQ ReDe[ R_PbR RTQSS RjNlS RYP\\Q RWPWS ReElEhM RXUSW RkVgW RVWaZ R_WZYR[',
	    'PoRFaF RSMXM R[MaM RTR`R RTU`U RRWbW RSISM RTOTU RWCWH RSIXIXL RZOZ[ R[I[L R]C]H RTO`O`U R[IaIaL RdDd[ RhMkPlT RdElEhM RkWfW',
	    'PoRFcF RWI^I RYS]S RWCWI RYLYS RXWY[ RYL]L]S R^C^I ReDe[ RbL_O R]W^[ R`Wb[ RjNlT ReElEhM RTWS[ RRMVLVSRR R`K`TcScT RkWgW',
	    'PoTJaJ R\\ObO RTVaV R[[b[ RTFTJ RSGaGaJ RSSaSaV ReEe[ RWCSG RVE]EZG RjNlS R^X`Y ReElEhM RUQ]Q[SYWWYS[ RUKUOSSTV R[W[[ R\\K\\O RaL\\M RkWgW RULZMTO',
	    'PoSFYF R\\FbF RTPTW RTPaPaW ReEe[ RSDRG R\\D[G R\\WbZ RVCUITN R_C^I[N RRIXIXMWM R[IaIaN_M RXYR[ RZRYX ReElEhMjPlSkWgW',
	    'PoRDbD RTKXK R\\K`K R\\NaN RSNXN RRYVY RRGRJ RRQR[ RRQVQVZ RZDZO RXQ\\Q\\Z R_Q_Z RRGbGbJ ReDe[ RjNlT ReElEhM RXQXY\\Y R_QbQbY^Y RkWgW',
	    'PoREbE RTLYL R[L`L RUQaQ RUS`S RUV`V RUYaY RTITL RVOV[ RWCWG RTIYIYL R[O[Y R]C]G R[I`I`L RdEd[ RVORS RjNlT RdElEhM R[I[L RkWgW',
	    'PoTGaG RUL`L RUOaO RUQaQ RXZ^Z R^[b[ RTST[ RTDTG RUJUR RXWX[ R[I[Q RXW^W]Z RaDaG RTSbSb[ ReDe[ RVHRL RXTVV R\\T`V ReElEhM RjNlSkWgW RZCZG[L',
	    'PoUJXJ R[J_J RSMaM RRObO RTU`U RZZaZ RXZ^Z RU[\\[ RR[Z[ RSDSL RTRTU RUEWEWK RZCZL R\\E^E]K RTR`R`U RaDaL RdDd[ RWWWY RhMjPlT RdElEhM R]W\\Z RkWgW RWGUG R\\G_G\\G',
	    'PoSFYF R\\FbF R[IcI R`McM RUQaQ RUTaT RUWaW RUOUW RUOaOaW ReEe[ RSDSG R\\D[G R]Xc[ ReElEhM RVCVISM R_C^I\\M RRIYIXLVL RXXR[ RjOlSkWgW R`I`M',
	    'PoSDYD R[DbD RTMbM RTSaS RWV[V R][b[ RTFTJ RVHVJ RWSWZ RXMXS RTFYFYJ R\\F\\J R]M][ R_H_J RTPaPaS R\\FbFbJ ReDe[ RjNlT ReElEhM RaU\\W RkWgW R[ZU[ RTMTUSZ',
	    'PoRElE RTUkU RTZkZ RTJT[ RbEbQhPhQ RTJkJk[ R\\E[OXR',
	    'PoRD^D R`ElE RZQ^Q RST^T RSY^Y Rb[g[ RSIS[ RWDWO RZDZP RSI^I^[ RgEg[',
	    'PoXIfI RaSfS RVVhV RVZhZ RVMV[ R\\I\\Q RbIbS RVMiMi[ R]EdDjJ RZDSK RZSXS',
	    'PoRD^D RST^T RSY^Y Re[j[ RSIS[ RVDVO RSI^I^[ RbNgS RdC`M RbIlHlTj[ RZEZP]P',
	    'PoRD^D RaMkM RZP^P RST^T RSY^Y Rb[k[ RSIS[ RWDWO R[D[P RSI^I^[ RbMb[ RaDkDkO RlVk[',
	    'PoRD^D R`JlJ RZP]P RST]T RSY]Y Rd[i[ RSIS[ RVDVO RZDZP RSI]I][ RiCi[ RbNeT RUQSQ',
	    'PoRE]E RYP]P RST]T RSY]Y Rb[k[ RSJS[ RVEVO RZEZP RSJ]J][ RbFbZ RfCfU RlWk[ RkJ^M RkIkThT',
	    'PoRD]D R`ElE R_NlN RYP]P RST]T RSY]Y RSIS[ RVDVO RZDZP RSI]I][ RfEf[',
	    'PoRD]D R`EkE R`NlN RYP]P RST]T RSY]Y Rb[f[ RSIS[ RVDVO RZDZP RSI]I][ RfEf[',
	    'PoYElE RePkP R[UkU R[ZkZ R[J[[ ReEeP R[JkJk[ R`E`N\\R RSDWF RRKWM RWRS[',
	    'PoZHdH RSNkN RbUiU RUWiW RUZiZ RURU[ RbNbT RURiRi[ RVKgKdN R`DlJ R\\TWV R]ERJ R_D]D R\\O\\T',
	    'PoRE]E RhNlN RYP]P R_SlS RST]T RSY]Y RSJS[ RVEVO RZEZP RSJ]J][ ReOe[ R_GhGiM RlKlN RaN_N RcCbL',
	    'PoRD]D RYP]P RST]T RSY]Y Rd[k[ RSIS[ RVDVO RZDZP RSI]I][ ReFe[ RlWk[ RiD_G RkK_L RlQ_S',
	    'PoRE]E RYP]P RST]T RSY]Y Rf[l[ RSJS[ RVEVO RZEZP RSJ]J][ R`I`L RfCfZ R`IkIkL RbW^[ RdR`Z ReMbX RlWl[ ReJcV',
	    'PoRE^E RST]T RSY]Y RaYlY RSJS[ RVEVO RSJ]J][ RaEa[ RlEl[ RcGdJjU RhKbW RUQSQ RZEZP^P RjDeS',
	    'PoRD]D RZP]P RST]T RSY]Y RSJS[ RVDVO RZDZP RSJ]J][ RaDhDhLlKlL RaPcUgYl[ R_PjPhTdY_[ RaF`L',
	    'PoRD]D RZP]P RST]T RSY]Y RSJS[ RVDVO RZDZP RSJ]J][ RhCh[ RaFeH R`MdN RlS^U',
	    'PoRD]D RbEgE RYP]P RST]T RSY]Y Rd[h[ RSJS[ RVDVO RZDZP RSJ]J][ RhFkN RbG^N RcObX R`OiOiUh[',
	    'PoRE]E R`FkF R_MlM RST]T RSY]Y Rh[l[ RSJS[ RVEVO RSJ]J][ RhMhZ RlVl[ RZEZP]P RcMcRaY',
	    'PoRD]D R_IkI R^PkP RYP]P RST\\T RSY\\Y RSIS[ RVDVO RZDZP RSI]I]Z RgVl[ RbV^[ ReCePiY RdQaY RdLcV',
	    'PoRD]D R`FkF R_MlM RYP]P RSU]U RdYkY RSY]Y R`ZhZ RSIS[ RVDVO RZDZP RSI]I][ RhRlZ RdP_Y RdMaX',
	    'PoRD]D R_KlK RYP\\P RST\\T RSY\\Y RSIS[ RVDVO RZDZP RSI\\I\\[ RbVfZ RaW^[ RgRk[ RdQ`Y ReCeNiY ReHhV ReIcT',
	    'PoRD^D ReJlJ RYP]Q RST]T RSY]Y R`ZkZ RSIS[ RVDVO RZDZP RSI]I][ R`Q`[ ReCeQ R`QkQk[',
	    'PoRD]D RYP]Q R`SfS RST]T RSY]Y Re[j[ RSIS[ RVDVO RZDZP RSI]I][ R`K`U R`KgKgS R_EkEj[ RTQRQ',
	    'PoRE^E RZP]P RST]T RSY]Y Rb[k[ RSJS[ RVEVO RZEZP RSJ]J][ R_H_K RbLbZ ReCeH R_HlHlK RlWk[ RkOaS',
	    'PoRD]D RaIlI ReNlN RYP]P ReTlT RST]T RSY]Y RSIS[ RVDVO RZDZP RSI]I][ ReHe[ RcC_M',
	    'PoRD]D R^IlI RYP]P RaQjQ RST]T RSY]Y RaZjZ RSIS[ RVDVO RZDZP RSI]I][ RaCa[ RjCj[',
	    'PoRD]D R_JlJ RYP]P RST]T RSY]Y R`ZkZ RSIS[ RVDVO RZDZP RSI]I][ R`Q`[ ReCeQ R`QkQk[',
	    'PoRD]D R_LlL RYP]P RST]T RSY]Y RSIS[ RVDVO RZDZP RSI]I][ RfEf[ RbS^V ReKjU ReKaU RjD`F',
	    'PoRE\\E R^JlJ RYP\\P RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ RbOdTkZ RhEjG R`E^J RcM_V RbOjOgUcY_[ RdH`S RdDaR',
	    'PoRE]E R_NlN RYP]P RST]T RSY]Y Rd[h[ RSJS[ RVEVO RZEZP RSJ]J][ R_G_J RhJh[ R_GlGlJ RaRdW ReCeG',
	    'PoTF`F RRJlJ RSMaM R\\T`T RTV`V RTY`Y RTPTZ RZCZJ R]M]S RTP`P`Y RgDjG RfU`[ RXMXSVT RgZj[ RjLeW RlVk[ RdCdLeW',
	    'PoRE^E RST]T RSY]Y RbZlZ RSJS[ RVEVO RSJ]J][ RcQc[ RbRlRl[ RcKfN RcG_L RdCaJ RcGkFhLbR^T RZEZP^P',
	    'PoRE]E RST]T RSY]Y RaZjZ RSJS[ RVEVO RSJ]J][ RaRa[ RaRjRj[ RbGdLhPlQ RcC`J RbGiFgKbP^R RZEZP]P',
	    'PoRE\\E R`MjM RYP\\P R_SjS RST\\T RSY\\Y R^ZlZ RSJS[ RVEVO RYEYP RSJ\\J\\[ ReMeZ RfFlL ReCgH RcG^L RdCbI',
	    'PoRE\\E RYP\\P RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ RgDg[ RlDl[ RdKdQ RiKjQ R_K^R RbDaR`X',
	    'PoRE]E R^GlG RYP]P RaQjQ RST]T RaUjU RSY]Y Rg[j[ RSJS[ RVEVO RZEZP RSJ]J][ RbJb[ RaMjLj[ RcF_P RcCaN',
	    'PoRE\\E RbIiI RYP\\P RST\\T RcThT RSY\\Y Rh[l[ RSJS[ RVEVO RYEYP RSJ\\J\\[ R_E_[ RcMcV RcMiMiT R_ElEl[',
	    'PoRD^D R`LlL RYP]Q R`TkT RST]T R`YkY RSY]Y RSIS[ RVDVO RZDZP RSI]I][ RaDaL R`P`[ R`PkPk[ RlJlL RlF`H',
	    'PoRD\\D R`IkI R^OlO RYP\\P RST\\T RSY\\Y Rg[l[ RSIS[ RYDYP RSI\\I\\[ ReCeO RhOh[ RlXl[ RVDVORR R`Z^[ RaD_L RcOaY',
	    'PoROlO RbUjU RTWjW RTZjZ RTRT[ RZCZM RcOcU RTRjRj[ RTDWG RbHeK RcC]I R\\RZTVU RZJRL R\\O[T R`FlFgK\\M',
	    'PoRE^E RaKkK RZP]P R_TlT RST]T RSY]Y R`[l[ RSJS[ RVEVO RZEZP RSJ]J][ RaDaK RfOfZ RaDkDkK RjN_O',
	    'PoRD^D R_MkM RYP]P RaTjT RST]T RaWjW RSY]Y RSJS[ RVDVO RZDZP RSJ]J][ RaPa[ R`GdJ RhJlN RhFaM ReDaG RaPjPj[g[',
	    'PoRE]E RaLiL RYP]P R^QlQ RST]T RSY]Y Rb[e[ RSJS[ RVEVO RZEZP RSJ]J][ ReLe[ RfFlL ReCgH RcG^L RiTkY RdCbI RaT^Y',
	    'PoRE\\E R_GhG R^KlK RYP\\P RST\\T R_UlU RSY\\Y Rb[e[ RSJS[ RVEVO RYEYP RSJ\\J\\[ RdCdK RfSf[ RiI_R RjEhJ R`PjPeR',
	    'PoRE\\E RaGlG RYP\\P R^QlQ RST\\T R_XlX RSY\\Y Re[i[ RSJS[ RVEVO RYEYP RSJ\\J\\[ RdSfU RdLfO RbC_J RaKjKjUi[ RaK`P_V R`O_Y',
	    'PoRD]D RaIkI R_NlN RYP]P RST]T RSY]Y R`ZkZ RSIS[ RVDVO RZDZP RSI]I][ R`R`[ RfCfN R`RkRk[ RaD`K',
	    'PoRE]E R_IkI RYP]P RbRjR RRT\\T RRY\\Y RRJR[ RVEVO RYEYP RRJ]J][ RgHgNkMkN RhFlI RdO^U RbRcUfYl[ RcC`I RcI`N RiSeX^[',
	    'PoRE^E RZP]P R_PlP RST]T RSY]Y RSJS[ RVEVO RZEZP RSJ]J][ RdGeL RbTdX RiFlL RaE_L RhMh[d[ RjDaE',
	    'PoRE]E R^GlG R`PkP RYP]P RST\\T R`UkU RSY\\Y Rh[k[ RSJS[ RVEVO RYEYP RSJ]J][ R`K`[ ReCe[ R`KkKk[ RhDkE',
	    'PoRE]E RYP]P R_SlS RST]T RSY]Y Rb[e[ RSJS[ RVEVO RZEZP RSJ]J][ ReQe[ ReGeK R`MjMeQ RjFlK RaE_K RjCaE',
	    'PoRE\\E R^GlG R^MkM RYP\\P RST[T RSY[Y Rh[l[ RSJS[ RVEVO RYEYP RSJ[J\\[ RdPdZ RiPi[ RgJkN ReGbJ^N RdCeG RlXl[ R`Q_Z',
	    'PoRE\\E R_EkE R_NlN RYP[P RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ[J[[ RdEdN RhEhN R_G`K RjGjK R_N^V][',
	    'PoRE[E R]ElE RXP[P RST[T RSY[Y Ra[c[ Rh[k[ RSJS[ RVEVO RYEYP RSJ[J[[ R^J^[ R^JcJc[ RfJf[ RfJkJk[ R_OaT RgPiT',
	    'PoRE\\E R_KjK RYP\\P R_PjP RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ R_G_[ R_GjGjP ReUkZ RjTfU RdPgW RcCeF ReY^[',
	    'PoRD^D R`IlI RaNkN RYP\\Q RaSkS RST\\T RaYlY RSY\\Y RSIS[ RVDVO RZDZP RSI\\I\\[ RaGa[ RfHfY RfCgF RbD^M',
	    'PoRE]E R`JiJ R^NlN RYP]P RST\\T RSY\\Y Rb[e[ RSJS[ RVEVO RYEYP RSJ\\J\\[ ReNe[ R_QbS RgTkY RkQhS ReOhW RcV^Y RaC`L RaFiFhN',
	    'PoRE[E R]JcJ ReJkJ RXP[P RRT[T RRY[Y RRJR[ RUEUO RYEYP RRJ[J[[ RaDa[ RhDh[ RaNdP RhIlW RaI\\W RhIdV RhGkU',
	    'PoRE\\E R]GkG RXP[P R_RjR RRT[T R_VjV RRY[Y Rd[k[ RRJR[ RUEUO RYEYP RRJ[J[[ R_L_X ReJe[ R]NjNjV RkXk[ RfIlN RaI\\N RcC`K',
	    'PoRD]D RaLiL R_PiP RYP]P RST]T R`WhW RSY]Y Rf[j[ RSIS[ RVDVO RZDZP RSI]I][ R`S`W RdLdW RhShW RbC^K RbJ`N R`HlHlUk[',
	    'PoRE]E R_GkG R^NlN RYP]P RST]T RSY]Y R`ZjZ RSJS[ RVEVO RZEZP RSJ]J][ R`R`[ ReCeG R`RjRj[ RiIgM RbHcM',
	    'PoRE]E RcGkG RaHiH R^HfH RYP]P RbSjS R_TfT RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ RlXk[ RdJhN RfPjP RiUeWgZj[ RfDjE RkKkNiN RgW^[ RbOdW RbCdK RhJcM^N',
	    'PoRE^E R_FlF RaMjM RZP]P R`QkQ RST]T R_UlU RSY]Y Ra[e[ RSJS[ RVEVO RZEZP RSJ]J][ RaIaM ReCeF ReTe[ RaIjIjM RjQeS',
	    'PoRE\\E RYP\\P RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ[J[[ ReVj[ RhKkN RdJ`N RcW_Z RlRhU RkFhI RdSgX RdJiK RaE_I RaQ_V ReCdL RdOcW',
	    'PoRE]E R_GlG RYP]P R^TlT RST]T RSY]Y RSJS[ RVEVO RZEZP RSJ]J][ ReCeG ReQe[ RaKdN RhLlP RiIeO RbI_P RTQRQ',
	    'PoRE\\E RXO[P R]PcP RRT[T RRY[Y RRJR[ RUEUO RYEYP RRJ[J[[ RgVl[ R`HcL RgHkM RiIdN R^SbW][ RiVd[ RaJ]N ReRhY ReEkEhK RdPkPgX R^EcE`L RcR_Y',
	    'PoRE\\E R^GkG R^MlM RYP\\P RST\\T R`UjU RSY\\Y R`ZjZ RSJS[ RVEVO RYEYP RSJ\\J\\[ R`P`[ RaCaM RhChM R`PjPj[',
	    'PoRD\\D R_IjI R_MjM RYP\\P R_SdS RST\\T RSY\\Y Rf[l[ RSIS[ RVDVO RYDYP RSI\\I\\[ R_D_M R_O_[ RgOg[ R_DjDkM RlXl[ RlRfT RdZ^Z',
	    'PoRD\\D R`HjH R`LjL R^PlP RYP\\P ReTkT RST\\T RSY\\Y Rd[l[ RSIS[ RVDVO RYDYP RSI\\I\\[ R`D`L RfPfZ R`DjDjL R`S^[',
	    'PoRE\\E R_HlH R`LeL RYP\\P RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ RaOaW RiDkF RgVcZ RgChOiReY RlWk[ RaOeOeVaV RjMhQiZ R_H_T][',
	    'PoRE\\E R_GhG R^MlM RYP\\P RST\\T RaUjU RSY\\Y RaYjY RSJS[ RVEVO RYEYP RSJ\\J\\[ RaPa[ RdCdL R`QjQj[ RkEiHbP]Q',
	    'PoRE\\E R]IdI RfKlK RXP[P RfRlR RST[T R^WcW RSY[Y Ri[l[ RSJS[ RUEUP RXEXP RSJ[J[[ R^P^Y RaCaO R^PcPcW RfElEl[ RfEfSeZ',
	    'PoRE]E ReHjH RdLlL RYQ]Q R`SjS RST]T R`WjW RSY]Y RSJS[ RVEVO RZEZQ RSJ]J][ R`O`[ RfDfL R_EkDkG RaF_M R`OjOj[g[',
	    'PoRE]E R`HjH R`MjM RYP]P R`RkR RST]T R`UkU RSY]Y R_[l[ RSJS[ RVEVO RZEZP RSJ]J][ R`D`M ReNeZ R`DjDjM RaN_T',
	    'PoRE\\E ReOkO RYP\\P RST\\T RSY\\Y R][l[ RSJS[ RVEVO RYEYP RSJ\\J\\[ R^G^J R_R_Z RcRcZ RgRgZ R_RjRjZ R^GkGkJ RkMkO RcLaO]P ReDcM',
	    'PoRG]G RROlO RaUjU RTXjX RTZjZ RTRT[ RYDYN RbObU RTRjRj[ RkEhH RXGSM ReIhLlM RXI\\J R`E^I RdIaL]N R\\TWV R\\O\\T RdDdJ R\\CSE',
	    'PoRE]E R_FlF RaNjN RYP]P RST]T R`UkU RSY]Y R`ZkZ RSJS[ RVEVO RZEZP RSJ]J][ R`Q`[ RaIaN ReCeG RaIjIjN R`QkQk[',
	    'PoRE\\E R_IjI R_NjN RYQ\\Q RST\\T RSY\\Y R]ZlZ RSJS[ RVEVO RYEYQ RSJ\\J\\[ R_Q_Z R_E_M RcQcZ RgQgZ R_EjEjM R_QkQkZ',
	    'PoRE\\E RXP[P R^SlS RST[T RSY[Y Rg[l[ RSJS[ RUEUO RYEYP RSJ[J[[ R`MhMh[ RlXl[ RgIlM RiDgG RlHhI RaW][ R^GaI]N RfDiL R_DcD_L RbMbSaX',
	    'PoRE\\E R_FjF R^JlJ R^PkP RYP\\P RST\\T RSY\\Y R][l[ RSJS[ RVEVO RYEYP RSJ\\J\\[ R_S_Z RcScZ RgSgZ R_SkSkZ RhLlP ReCdJaO',
	    'PoRD\\D R_JbJ RgJkJ R_NkN RYQ\\Q R^RjR RST\\T RSY\\Y RSIS[ RYDYP RSI\\I\\[ R_F_N ReCeR RgFkFkN R`SdXl[ RjSeX^[ RbE_F RVDVMUQ',
	    'PoaMkM RXPZP RSTZT RSYZY Rc[l[ RSJS[ RREYEXP RSJZJZ[ RfCfX RjRkU RfNiQ R\\M_M_X\\[ R]F_J RaEcJ R_Xe[ RfLbU RkEiJ RUEUMUQ',
	    'PoRE\\E R]FlF R`PiP RYP\\P RST[T R_UjU RSY[Y R^ZlZ RSJS[ RVEVO RYEYP RSJ[J\\[ RaCaI RdPdZ RhChI ReIjO RdI^P',
	    'PoRE]E R_KjK RYP]P R_PjP RSS\\S RfVlV RSX\\X Re[l[ RSISZ RVEVO RYEYP RSI\\I\\Y R_G_P ReGeZ R_GjGjP RaW][ RjTlW RhRgV ReCcG RbPaX',
	    'PoRE\\E R_GlG R_KkK R^PlP RYP\\P RaTkT RST[T RSY[Y R_ZlZ RSJS[ RVEVO RYEYP RSJ[J[[ RfTfZ RbS^W RbCbF RhCgG RdM`V ReGcR',
	    'PoRE\\E R_NjN RYP\\P RST\\T RSY\\Y R][l[ RSJS[ RVEVO RYEYP RSJ\\J\\[ R_R_Z R_E_N RcRcZ RgRgZ R_EjEjN R_RkRkZ RdIhL ReFbL',
	    'PoVOiO RbUjU RUWjW RUZjZ RSLSP RUSU[ RcOcU RUSjSj[ RSLlLlP RkEiG RUESG R^E\\G RbE`G RXH]J ReHlK RfDdH`J R\\TWV RYDWIRK R\\O\\T',
	    'PoRE\\E R]KlK RXP[P RST[T RSY[Y R][l[ RSJS[ RVEVO RYEYP RSJ[J[[ R_R_[ R_F_K RbRb[ RfRf[ R_FjFjK R_RjRj[ ReCeKhOlQ RdKaO]Q',
	    'PoRE\\E RfKlK R`PlP RXP[P RdSkS RST[T RdWkW RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ R^F]I RdOd[ RdCdF RgFgK R^FlFlI RaO]S RbFaL_R RaJ]L',
	    'PoRE[E R]FlF R`PjP RXP[P RRT[T RRY[Y R][l[ RRJR[ RUEUO RYEYP RRJ[J[[ R_S_Z RaIaO RcScZ RfSfZ R`KjKjO R_SjSjZ RaI]M RcC`K',
	    'PoRE\\E R^FlF R^MlM RYP\\P R`QjQ RST[T RSY[Y R`ZjZ RSJS[ RVEVO RYEYP R^F^V R`U`[ ReHeT R`JjJjQ R`UjUj[ RdCgF RSJ\\J]V][',
	    'Po[JlJ RcOgO RSPkP RbVjV RTXjX RTZjZ RTST[ RXCXO RbPbV RgGgO RTSjSj[ R\\F^H RiFfH RSEUH R[EcEbH R]LaM RXKRL R\\P\\UVV RiCbD',
	    'PoRE\\E R^ElE R_MkM RYP\\P R_PkP R^TlT RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ[J[[ R_H_M RcEcM RgEgM R_HkHkM RiVlZ R`V]Z ReTe[a[',
	    'PoRE\\E R^HkH RYP\\P RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ RdKjQ RgMkLkN RhEkI RcDaH RdK^Q RhIhN R_J^N RdQ_S RfT`W RjUhW`[ RcM]N',
	    'Po_DlD RRE\\E RcMiM RYP\\P RST[T RaWeW RgWkW RSY[Y R_ZlZ RSJS[ RVEVO RYEYP RSJ[J[[ R_D_Z RaPaW RcHcM RaPePeW RgPgW RcHiHiM RgPkPkW',
	    'PoRE\\E R]FlF R_NjN RXP[P RRT[T R`WiW RRY[Y RRJR[ RUEUO RYEYP RRJ[J[[ R^R^[ R_H_N ReCdF RjHjN R^RkRk[ RgGbM RgTiX RbIgK RdNbW Rh[k[h[',
	    'PoRE\\E R^FlF R_LkL RYP\\P R_PkP RST[T R`VjV RSY[Y R`ZjZ RSJS[ RVEVO RYEYP RSJ[J[[ R_I_P R`S`[ RcCcP RgCgP R`SjSj[ R_IkIkP',
	    'PoRE\\E RYP\\P RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ[J\\[ R^EcEcL ReEkEkM RiFfI RjIfM RaF^I RbI^M ReMgPlR RdMaQ]R RdR_T RgT`X RiWeY^[',
	    'PoSD^D RVG]G RhHlH RUI^I RSN_N RRQlQ RbVjV RTXjX RTZjZ RSDSN RTST[ RbQbU RTSjSj[ RbDhDhH RYJ^L RbDaH RYKUL RaJjIgLaO RbIcLgNlO RYGYK R\\Q[UVV',
	    'Po`EjE RTGYG RRJYJ R\\JlJ RcOgO RRQlQ RbVjV RTXjX RTZjZ RTST[ RYCYO RbQbV RgIgO RTSjSj[ R^LaM R]FaFeG RTJSO RTDTH RdD[F R\\Q\\UVV RgG\\I',
	    'PoRE]E R^IlI R_MkM RYP]P R_QkQ R^TlT RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ RcCcI ReMeVj[ ReSgY RbJbM R`D`G RjDiG RgCgIgM RdVaY^[',
	    'PoRE\\E R_HkH R_LkL R_PkP RXP[P R_TlT RST[T RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ R`G`T RbWb[ ReGeT RfWg[ RjVl[ RaC\\L R_V][',
	    'PoRE\\E R]FlF R^JkJ RXP\\P RRT[T RRY[Y R][l[ RRJR[ RVEVO RYEYP RRJ\\J\\[ R_S_Z RcScZ RdLdP RfSfZ RiLiQ R_SjSjZ RhHkK R`N]Q RbFaJ R`L_P RlOlQiQ',
	    'Po^DlD RRE\\E RcKgK RYP\\P R_QkQ R_TkT RST\\T R^WlW RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ R_H_L R_N_T RcDcK ReTe[ RgDgK R_NkNkT R_HkHkL',
	    'PoRE\\E RXP\\P RiQlQ RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ[J[[ ReLiLjQ ReVj[ RgGkL RiDgF RkGhH ReChI R_DcD`J RdSjSiWfZc[ Ra[^[ ReLdQ R^GbH]LbLbP^P]UbTa[',
	    'PoRE\\E R^HkH R^LkL R^NkN RXP[P R`QjQ R^TlT RRT[T RRY[Y RRJR[ RUEUO RYEYP RRJ[J[[ R^E^L RaTaZ RbCbL RgCgL R^EkEkL ReVjZ ReTgX RkVgW RfZ_Z R^N^V][',
	    'PoRE\\E R_IkI R_LkL R^OlO RYO\\P R^TlT RRT\\T R_WkW RRY\\Y RRJR[ RVEVP RYEYP RRJ\\J\\[ R_D_I RcEcI ReIeO ReTe[ RgDgI R_DkDlI RbQbT RhPhS',
	    'PoRE\\E R_IkI R_MkM R^OlO RYP\\P RST[T R`VjV RSY[Y R^[l[ RSJS[ RVEVO RYEYP RSJ\\J\\[ R_F_L R`R`V RbWc[ RcCcL RgCgL R`RjRjV R_FkFkL RhWg[',
	    'PoRE\\E RdEjE RcKiK RdNjN RaPlP RXP\\P RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ\\J\\[ ReCeM R_HlHlJ RkShT RjYlY ReQ`S ReT`V ReW_Y RgPiW RdQfUfXf[b[ R_H_T]Z',
	    'PoRE\\E RaJhJ RXP[P R^RcR RfRjR RST[T RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ R^M^R R^McMcR RfMfR RfMjMjR RhWl[ R`VdY RdCkK RdD]K RhTeZ RaT^[',
	    'PoRE\\E R^FkF R]JlJ RXP[P R_PjP R_SjS RST[T RSY[Y RSJS[ RVEVO RYEYP RSJ[J[[ R_M_S R_MjMjS RcUfW RaGaI RjVlZ RdCeF R_V]Z RhGgJ RbVa[iZi[',
	    'PoRE\\E R]FlF R^LkL R^OkO RXO[O R]SlS RST[T RSY[Y RSJS[ RUEUO RYEYO RSJ[J[[ RbUbZ RbKbS RdCeF RhKhS RfVk[ RkUfV ReSgX RgHkJ RbH]J ReSbT]W RgY^[',
	    'PoRE\\E R_ElE R`JcJ RgJjJ R_McM RgMjM RYP\\P R^PlP RST\\T RSY\\Y RSJS[ RVEVO RYEYP RSJ\\J\\[ R_T_[ RdTd[ ReEeN RgTg[ R^H^IlHlJ R_TlTl[i[ RdPcW',
	    'PoRE\\E R^GlG R_PkP RXP\\P R_SjS RST[T R^VlV RSY[Y RSJS[ RVEVO RYEYP RSJ\\J\\[ R_J_P RbXc[ R_EeEeV R_JkJkP RiXlZ RhLgN RfXg[ RaLbN R_X^[ RjCbD',
	    'PoRE\\E R^EkE R_HjH R`MiM R^OkO RXP[P R]RlR RST[T ReUlU R^YcY RSY[Y Rg[j[ RSJS[ RUEUO RYEYP RSJ[J[[ R^T^Z R^TcTcY ReCeH RdMdO RjSj[ ReWgY R]JlJkM',
	    'PoRE\\E R]FlF RaIgI RXP[P RbSfS RST[T RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ RaCaI RbMbS RbWc[ RbMfMfS RfWg[ RgCgI RkMiO RjWk[ R_W][ R`K`S]S R]N`N]N RiKiTlT',
	    'Po_DlD RRE\\E R`KcK RgKjK RgNkN R_NcN RXP[P RST[T RSY[Y RcZgZ RSJS[ RUEUO RYEYP RSJ[J[[ R^H^K RcQc[ ReDeO RcQgQg[ RiQi[ R^HlHlK RlQhR RaQ^Q^ZaZ^Z RlQlZhZ R^QaQaVaZ',
	    'PoRE\\E R^FlF RfIlI RXP[P R`TlT RST[T RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ RcGcN ReOe[ RiGiN RgUkZ RaPbR RhIkL RfT`Z RjPiR ReCeF R`IdJfK RiHfM RbH`M R^F^S\\[',
	    'PoRE\\E R^FlF R_IdI RfIkI RXP[P RST[T R_VkV RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ R`K_O RbGbO RdKdN ReDeF ReVe[ RfKfO RhGhO RkKkO RiXkZ R`ReT RiTkW RdPbS RbX_Z RhRcV R^F^S\\Z',
	    'PoRE\\E R^ElE RfKkK R^NkN RXO[O R_QkQ R]TlT RST[T RSY[Y RSIS[ RUEUO RYEYO RSI[I[Z RbVbZ R^H^KbKbS R^HcHcK ReCdF RfHgKhS RfHkHkK RfVj[ ReTgX RbU\\X RlVgX RfY^[',
	    'PoUFZF RUHZH RdHhH RVMgM Ra[e[ RSKSN RVDVJ R[D[J R[DbDbJ RVNeOhOhS RdDiDiJ RSKkKkN RWXfXe[ RaMaP R\\M[PXQ RXVRX RZDUD R[VdVlX ReFiF RbTeS RYT[TYS R\\YT[ R_TbS R^T\\T_S R\\G`H[I RVNVSWShSeTbT_S',
	    'PoRE\\E R^MkM RXP[P R^RdR ReRlR RRT[T R^UdU R^WcW RRY[Y R^YdY RRJR[ RUEUO RYEYP RRJ[J[[ R^J^M R^P^[ R^PdPdT R^JkJjN RiElI RfTgX RcFdH R_E]J RbJcM RfJgM RjOj[g[ RiD_E',
	    'Po^DdD ReDlD RRE\\E R^MlM RXP[P R^TkT RST[T RaWeW RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ R^G^K RaTaZ RaH`K RbMbS RfGfK RhHhK RfGkGkK R^PkPkT RkVfX R^GcGcK ReZ_Z RgMg[lZl[ R^M^U][',
	    'PoRE\\E R^FlF R`IeI RfIlI RXP[P RgRkR RST[T R`UcU RgUkU RgXkX RSZ\\Z RSJS[ RUEUO RYEYP R^F^U R`K_P RbGbO ReCeF RfKfPg[ RhGhO RkKkO RcQcX^X RbZ`[ R`RdS RSJ[J\\U\\Z',
	    'PoRE\\E R^HcH RfHkH R^JlJ R`MeM RXP[P RST[T RSY[Y RSJS[ RUEUO RYEYP RSJ[J[[ R^D^H R`P`Y RbMbP R^DcDcH R^PePd[ RfDfH RfDkDkH RgSkZ RjSg[ RgQiY RgPkPhY RhLfT RdR`S R`UdU`U ReX_Y R^J^T\\[',
	    'Po[FbF RROlO RTFYF_F_[ RWHYM R_NgVlX RfHdM R_NXVRX RgD_E',
	    'PoRPlP R_M_[ RVHXM R^G_L R_PfWlY R_PXWRY RhD_FVF RiGgKeM',
	    'PoRN^N R`OjO RYDY[ R`DjDjN RYO]T RUGUK RYNTV R\\G[L R`D`Q][ R\\DRE RePhWl[',
	    'PoRM]M R`QlQ R`ZlZ RXDX[ R`I`[ RfCfZ R`IlIl[ RWMZQ\\S RTGUK RXMSW R[G[L R[DRE',
	    'PoRM\\M R_RjR R^VlV RXDX[ ReOe[ RWM\\R RTGUJ R`FeLlN RXLTU R\\GZJ R^EkEeK]N R\\CRE',
	    'Po^IkI R_LjL RRM\\NlO R^TlT R^WkW RXDX[ R^D^I RbDbI RdIdO RdTd[ RgDgI R^DkDkI RWO[S RSGTK RaPaT RhOhS RWMRW R[GZL R[CRE',
	    'PoUJiJ RUPiP RTUjU RR[l[ RUEUP R_E_[ RUEiEiP',
	    'PoRHlH RUOiO RUSiS RTWjW RR[l[ RULUS RULiLiS RhD_EVE R_E_[',
	    'PoSJ_J RSN_N RSS_S Ra[e[ RSESN RYEYY RSE_E_N RfNf[ RdIiM RaEkEfJ R`NlNiS R_XRZ',
	    'PoVGhG RVJhJ RRLlL RURiR RUUiU RTXjX RR[l[ RUOUU RVDVJ R_O_[ RVDhDhJ RUOiOiU',
	    'PoTE^E RbFlF RRH^H RUNkN RYSiS RYUiU RXXiX RV[k[ RYPYU RYCYL R`P`Z RYPiPiU RVKRL RYGTL RcE`H RWH^K RbFeJlL RjFfJ`L RUNUTTY',
	    'PoXLfL RTRjR RR[l[ R_L_[ RXTYY R_CeJlM RfTdY R_CXJRM',
	    'PoVKaK RTPbP R[K[Z RVSWX R[EcI RaR_W R[CWHRJ RbYT[',
	    'PoUJ]J RSO^O Rb[k[ RYJYY RcDc[ RYC^H RWERJ R]R\\W RlVk[ R_XRZ RURUW',
	    'PoTJ\\J RSO^O R`[k[ RXJXZ RYC^H RlWk[ RXCWFRJ R\\R[W RfL`Y RgJcR R`FjFfM R^YR[ RURUW',
	    'PoUJ]J RSO_O Ra[e[ RYJYY RfLf[ RYE\\F^I R`FlFeM RYCSJ R]R\\W RURUW R^XRY',
	    'PoUJ]J RSO^O Re[j[ RYJYY RYC^H RaW][ RXDRJ R]Q\\V RURUV RcQ_YSZ R`ElElSj[ RdEdKbU',
	    'PoVJ^J RSO`O Rf[l[ RZJZY RdFdU RlDl[ R[C`I RZCRK RURVW R^R]V RaXSZ',
	    'Po_FlF RTJ]J RSO^O Ra[f[ RXJXY RgFg[ RYC^H RYCRJ R\\Q\\V RURUV R_XRZ',
	    'PoUJ]J RSO^O RYJYY RcCc[ RYC^H RWERJ RcLlP R\\Q\\V RURUV R_XRZ',
	    'PoTJ\\J RSO]O Rh[l[ RXJXY R^JhJi[ RlVl[ RXC]H RWERJ R[Q[V RaR^Z RTRUV R]XRY RbCbOaV',
	    'PoUJ]J RSO^O Rf[j[ RYJYY RYC^H RbW^[ RXERJ RURUW RdR`Y R]R\\V R^XRZ R`JlJlTj[ ReCeLcU',
	    'PoXPgP RTTjT RS[k[ R_P_[ RfVdZ RXVZZ RcDiF RZDWGSH R[G^JeNlO RcG`KYNRP',
	    'PoTJ\\J R_MlM RSO^O RYJYX RfDf[ RYD^H RYDRJ R\\Q\\V RURUV R^XRY',
	    'Po`FeF RTJ\\J RSO^O RXJXY R\\Q\\V RXC]H RaV\\[ RWERJ RgRjZ RdO_X RURUV ReIbU R^XRZ ReGgV',
	    'PoTJ\\J RSO]O RXJXY R\\Q[V RXC]H RWERJ RWCUG R`S\\[ RTRUV R^XRY RaEgEgKiX RaI_V',
	    'PoTJ\\J RSO]O Rh[l[ RXJXY R`EhEh[ RlVl[ RZE]I RYDRJ R`E`T\\[ RTRUW R\\R[WRY R`M_X',
	    'PoYPeP RUTjT RS[k[ R_P_[ RcDgEjI RfVdZ RYVZY R_HdMlP R^HYMRQ RZDVHRI',
	    'PoTJ[J RRO\\O Rd[j[ RXJXY RXD\\H RXDRJ RTRUV R[RZV R`R\\Z R\\XRZ R^EhEgNlNlSj[ RaEaL_V',
	    'PoUJ]J RSO^O Rd[j[ RYJYY RYD^H RYDRJ RcNeT RcC_M R\\Q\\V R^XRZ RURUW RaIlHlQj[',
	    'PoUJ]J RSO^O RYJYY RYC^H RXERJ R\\Q\\V RiDfF`I RjKfO`Q RlRgW_[ RURUV R^XRY',
	    'PoTJ\\J RSO]O Ra[e[ RXJXY ReDe[ RXC]H RWERJ RURUV R\\Q[V RiJlT R`J^T R^XRY',
	    'PoUJ]J RSO^O RaYlY RYJYY RaEa[ RaElEl[ RYC^H RXDRJ R]R\\V R^XSZ RURUW',
	    'PoUJ]J RSO^O RYJYY RfEfX RlCl[ RYC^H RWERJ R\\Q[V RURUV R_XRY RaDaQ_X',
	    'PoTJ\\J R_OlO RRO]O Ra[e[ RXJXY ReKe[ RYE]I R`FkFeK RXDRJ R\\R[V RTRUW R]XRZ',
	    'PoTJ\\J RSO\\O Ri[l[ RXJXY R`EiEiZ RlWl[ RXC\\H RbLfQ RVFRJ RTRUV RWCUH R[Q[V R\\XRZ R`E`R_X',
	    'Po`EkE RUJ]J R_NlN RSO^O Ra[e[ RYJYY RfEf[ RYC^H RXERJ R\\Q\\V RURUV R^XRY',
	    'Po^EkE RTJ[J R]KlK RRO\\O Rc[h[ RXJXY RXD\\H RXDRJ RTRUV R[RZV RbL`QjQh[ R\\XRZ',
	    'Po`EkE RUJ\\J R_NlN RSO^O RXJXY ReEe[ RYC^H RWERJ R\\Q\\V RURUV R^XRY',
	    'Po_FkF RTJ\\J RSO^O R^[l[ RXJXZ ReFe[ RYC^H RXCWFRJ R\\R[W R^YR[ RURUW',
	    'Po_JlJ RUJ]J RSO^O Rb[g[ RYJYY RgCg[ RYC^H RWERJ R\\Q\\V RkLfR^U RURUV R^XRZ',
	    'Po`JlJ RTJ\\J RSO]O R`[l[ RXJXZ R`J`[ ReCeI RXC]H RWFRJ RWCVH R\\R[W R^YS[ RURUW',
	    'PoUJ\\J RSO]O Re[j[ RXJXY R\\Q[V RYC]H RcV][ RWERJ RePaY RaK_Q R]XRY RURUV R_ElElSj[ RfEfKdU',
	    'PoTJ\\J R_KlK RSO]O RXJXY RYC^H RbV][ RWERJ RfRjZ ReCePaX R\\Q\\V ReJhW R^XRY RTRUW ReJcU',
	    'PoTK\\K R_OjO RRP]P R`[k[ RXKXZ R`E`[ R_EjEjQ RXD]I RlWk[ RXDRJ R[RZW R\\YRZ RTSUX',
	    'PoaHlH RTJ\\J RSO]O R`[k[ RXJXY R[R[W RkWk[ RXD\\H R`MjM`V_Z RXDRJ RbD^M R\\XRZ RTRUW',
	    'PoUK\\K RSP]P RXKXZ RXD]I RfEjH RlWk[ RYDRJ RgZj[ R\\R[W RUSUW R^YR[ RcDcJeV RjK`M',
	    'PoTJ[J RSO\\O RXJXY RXC]H RbHeK RVFRJ RTRUV R_KdThYl[ RWCUH R[Q[V RjJeT`Y\\[ R^EkEgQ R]XRZ',
	    'PoUJ\\J RSO]O RgTjT Ra[k[ RXJXZ RaFa[ ReCeU RkIkT RXC]H RVFRJ RTRUW RWCUH RlWk[ R[R[W RkI^M R]YR[',
	    'PoUJ\\J R_LkL RSO^O R^[l[ RXJXZ ReDe[ RYC^H RWERJ R\\R\\W RURUW R^YRZ',
	    'PoTJ\\J RSO]O RXJXY RXE[F]H RYCRJ RkJgU R\\R[V RdE_P RdNhRbY^[ RTRUW RcHlHiQ RdCbL R]XRY',
	    'PoTJ\\J R]JlJ RSO\\O RXJXY RfWk[ RXC\\H RVFRJ RhOeW RWCUH R[QZV R_SfUbY\\[ RTRUV RiKfU RcD^T R\\XRY',
	    'PoTJ\\J R`NlN RSO]O RXJXY RgEg[ RXD[F]H RYCRJ R\\R[W RjD`E R]XRY RTRTW',
	    'PoTJ\\J RSO]O RXJXY RlVl[ RaOfU RXC]H R`W][ RWERJ R\\Q[V RbR_Z RTRUV R_IiIj[i[ R]XRY RcCcLaV',
	    'PoTJ\\J RSO\\O Rd[i[ RXJXY RXC\\H RaV\\[ RWCVGRJ R_IcQgUlW RcP_Y RTRUV R[Q[V R\\XRZ R^EkEkSi[ RdEdKbU',
	    'PoTJ[J RSO[O RWJWY RZQZV RcHcZ RWC\\H RUGRJ RVCTI RTRTV R[XRY RiDiNkZ R^F^Q]Y RjD^F',
	    'Po_ElE RTJ\\J R_OlO RSO\\O Rc[h[ RXJXY RhEh[ RZE]H RhMgQ_X RYCRJ R[R[W R`G_N R\\YRZ RTRUW',
	    'PoTJ\\J R`MlM RSN]N RXJXY RgMg[ RXC]H RWERJ R\\Q[V RURUV R`F`S][ RjD`F R^XRZ R`N_Y',
	    'PoUJ\\J RSO]O R`OjO R`[k[ RXJXY R`E`[ ReEeO R`EjEkQ RXC]H RkWk[ RVFRJ RWCUH R[R[W R]XSZ RURUW',
	    'Po^HlH RUJ\\J RSO]O Rd[i[ RXJXY ReCeH RXC]G R`W\\[ RWERJ R\\Q[V RTRUV R]XRY RbNjNjUi[ RbHbOaW',
	    'PoTJ[J RRO\\O Rf[l[ RXJXY R_I_L RfCfY R_IkIkL RXD\\H RbW^[ RWDRJ RTRTW RdQaY R[RZV RlWl[ R\\XRZ ReIcV',
	    'Po_ElE RTJ[J R^OlO RRO\\O RXJXY RiEi[ RXD\\I RWERJ RTRTW RZRZV RbEbQ`X R\\XRZ',
	    'PoTJ\\J RSO]O RXJXY R\\Q[V RhCh[ RXC]H RWERJ RURUV R`MeO RaFeH R]XRY RlS^U',
	    'PoUJ\\J RaKlK RSO^O RaSlS Rh[l[ RXJXY RaElEl[ RYC^H RWERJ R\\Q\\V RTRUV RaEaR`X R^XRZ',
	    'PoaEgE RTJ\\J RSO\\O RbYkY R`YhY R^ZeZ RXJXY RXC]H RWERJ RhSk[ RgFkO RWCUG RaH^O R[Q[V RcN`Z R]XRZ RTQUV',
	    'Po_IkI RTJ\\J R^OlO RXJXY RhWl[ RXC]H RcU][ RWFRJ ReCeRiY RdQaX RWCUH R[Q[V RTRUV ReNgV R]XRY RRO[O`O RdLcU',
	    'Po^FlF RTJ\\J RSO]O RXJXY ReIe[ RZE]I RcM^R RYCRJ RhLiNkR R[R[W RfFaP R]XRZ RTRUW',
	    'PoUJ\\J RSO\\O RXJXY R[QZV R_J_[ RXC]H RWCVFRJ RdMaT ReHcR RTQUV R\\XRY R_JlJl[h[ R`DeDgQ',
	    'PoTJ[J RSO\\O Rb[i[ RXJXY R[Q[V RcIc[ RiWi[ RXC\\H ReEhH RVFRJ RhLlT RbDgF RWCUH RTRUW R_M^U R\\XRZ',
	    'PoTJZJ RRO[O Rf[l[ RTRTW RWJWY RaGa[ RfDfZ RWD[H RkIdQ RWERJ RZRYV RbD]N RlWl[ R[XRY',
	    'PoTJ\\J RSO\\O R^PlP RXJXY R^HjHjP RfVk[ RXC\\H RbV][ RVFRJ RdCdQhX RWCUH RTRUV R[Q[V R]XRZ RdMcV',
	    'PoUJ\\J RSO]O R_TkT Re[l[ RXJXY R[Q[V R_K_U ReCe[ RkKkU RlXl[ RXC]H RXCRJ RURUW RjF^G R]XRZ',
	    'PoTJ\\J RSO]O R^UhU Re[j[ RXJXY RXC]H RWERJ RTRUV RfQhV RbC_K R\\Q[V RcLaU R^XRY RaHlHlSj[',
	    'PoTJ[J R`KfK RSO\\O Ra[e[ RXJXY R_DgDgK RlDl[ RXE\\I RXDRJ R[RZW RTRTW R\\XRZ R`K_QgQe[',
	    'PoTJ\\J R`LiL RSO]O RXJXY RXC]H RfElK RWCVFRJ RcG]L RdCbH R_QjQg[ R\\Q[V R]XRZ RTRUW',
	    'Po_EkE RTJ\\J R_LkL RSO]O RXJXY ReUj[ RXC]H RhTbZ RWERJ RbNfW RjNfW RWCUG RTQUV RbLdU R[Q[V R]XRZ R_E_R^Z',
	    'Po]EhE RTJ[J RSO\\O RXJXY RdTjZ RXC\\H RVFRJ R`EaNeW RWCUH R`MkMhTcY_[ RTQUV R[QZV R]XRZ R`K^X RhFgM',
	    'PoTJ\\J RSO]O R_PlP R_[l[ RXJXZ RfFf[ RXC]H RWFRJ RWCVH R\\R[W RTRUW RkD_F R]YRZ',
	    'PoTJ\\J RSO]O RaSeS RXJXY ReCeR RXC]H RWERJ RiHlN R\\Q[V RjPhUeX^[ RURUV RaH_N R]XRY',
	    'PoTJ\\J RSO]O R^PlP R][l[ RXJXZ RbSa[ RXC]H RVFRJ RWCUH R\\R[W R]YRZ RTRUW R_EiEiVh[ RcEcJcNbRaW',
	    'PoTJ[J RSO\\O Rd[h[ RXJXY RXC]H RaW][ RWCVFRJ RbEgDkM RaF]M RTQUV R[Q[V RcOaX R\\XRZ R_OjOiUh[',
	    'PoTJ\\J RSO]O Rf[l[ RXJXY RgNg[ RXC]H R]KdKjJkL RgFkJ R`W\\[ RWERJ RlWk[ RcC_K R\\Q[V RTRUV R]XSZ RaN`X',
	    'Po]HlH RTJ[J RRO\\O RXJXY RdCdN RWC\\H RVFRJ R`NbTgYl[ RVCTH R^NjNhScX\\[ RTRUV R[QZV R\\XRY',
	    'PoeGlG RUJ]J RSO^O RYJYY ReCeM RdTjZ RYE\\F^I RhTaZ RYCRJ RbNeV R`MkMgU R\\Q\\V RURUV R^XSY',
	    'PoTJZJ R^McM RSO[O Rf[l[ RWJWY R^D^[ RfDf[ RlXl[ RXD[H RXDRJ RZRZW RlKeN RcY]Z R[YRZ RTRUW',
	    'PoTJ\\J RfKkK RSO]O RXJXY RYE]H RgDjG RgS_Z RYCRJ RgZj[ RiMfT R[R[V RTRUV RlWk[ R]XRZ RcCcIeW RfJ_K RiJbK',
	    'Po]IlI RTJ[J RSO\\O R`ZkZ RXJXY RXC\\H RWCVGRJ RhSl[ RTQUV R[QZV R`N][ ReMbY R\\XSZ RbE_W',
	    'Po`JkJ RTJ\\J RSO]O R^QlQ RXJXY R[Q[V ReDe[ RXC]H RWCVFRJ RaE^N R]XSZ RTQUV',
	    'PoUJ]J RaNhN RSO^O Rd[j[ RYJYY RYC^H RXDRJ RcC_L R\\Q\\V RURUV RhS`U RaIlHlRj[ R_XRZ',
	    'PoUJ\\J R`LlL RSO]O RgYkY RXJXY ReEe[ R_ElElK RXC]H RWERJ R\\Q[V R^XRZ RTRUW R_K_RlQkX',
	    'PoTJ[J RhLkL RRO\\O RTRTW RXJXY RaDhDhL RXD\\H RWDRJ R_PaTeXl[ RZRZV R^PjPhSdX\\[ R\\XRZ R`D`L',
	    'PoTJ[J RRO\\O R_RlR RXJXY R_I_T RfCf[ R_IlIlT RXD\\H RXDRJ R[RZW R\\XRZ RTRTW',
	    'Po^IkI RTJ[J RRO\\O RXJXY ReCe[ RXD\\I RWERJ ReIjU R[RZW RdI_U RTRTW RdGbQ R\\XRZ',
	    'Po^ElE RTJ\\J ReJkJ RSO]O R_QlQ Rf[j[ RXJXY R_I_S ReEeQ RXC]H RVFRJ RWCUH R\\Q[V RTRUV R]XRY RlQj[',
	    'PoUJ]J RSO^O R`OlO R`YlY RYJYY R`E`[ R`ElEl[ RYC^H RXERJ R\\Q[V RURUV R^XSZ',
	    'PoTJ[J RSO\\O RXJXY RfVl[ RXC\\H RbU][ RVFRJ ReCeQhX RkIhO RWCUH RdPaX RTRUV R[Q[V R`I^P R]XRZ RdLcU',
	    'PoTJ[J R^KkK RSO\\O RXJXY RcUfX RXC]H RaV\\[ RVFRJ RfPjZ RWCUG RcP_Y RdCdLhW R[Q[V RTQUV R\\XRZ RdIbT',
	    'Po^HkH RTJ[J RRO\\O Rh[l[ RXJXY R[RZW RaLhLi[ RXD\\I RWDRJ RcCdF RlWl[ RTRTW R\\XRZ RaL`T_Y',
	    'Po_EjE RTJ\\J R^MlM RSO]O Rg[l[ RXJXY R\\Q[V RhMh[ RXC]H RaW][ RlWl[ RWCVFRJ R]XRY RTRUW RbMaX',
	    'Po_ElE RUJ\\J RSO]O R`QkQ RXJXY R`JkJkR RXC]H RWERJ R\\Q[V RURUV R]XRY R`J`S_Y',
	    'Po_ElE RTJ[J RRO\\O RgSkS Rc[k[ RXJXY RdJd[ RcJkJkS RXD\\I RlWl[ RXDRJ RTRUW R[RZW R\\XRZ R_E_S^Z',
	    'PoTJ[J RRO\\O RXJXY ReTkZ RXD\\H RaY][ RfT_Z RXDRJ RTRUV RaLfV RdDeH RiMdV R[Q[V RaIdS R^IjIfT R\\XRZ',
	    'PoTJ\\J R`NkN RSN^N RXJXX R`EkEkN RXC]H RWCVFRJ RfQjZl[ R\\Q[U RUQUV ReNhX R]WRY R`E`Q_X',
	    'PoYG_G RaMkM RZSdS RVVhV RS[k[ RTETM RYCYL R_S_[ RbCbL RYX[[ RkJkM ReXc[ R_NdQlS RjFaG R_NYQRS R^LRM',
	    'PoTJ[J R_JkJ RSO\\O Rc[k[ RXJXY RcLcZ R_EkEkJ RXC\\H RlWk[ RVFRJ RWCUH RkObS R[QZV R]XRZ RTRUW R_E_Q]Z',
	    'PoUJ\\J R_NlN RSO\\O R_YlY RXJXY R_Q_[ R_F_M ReCeY RlFlM RlQl[ RXC]H RWCVFRJ R[Q[V R]XSZ RURUV',
	    'Po]GlG RTJ[J RSO\\O RXJXY R^M^W RdCd[ R^MkMkW RXC\\G RVFRJ RWCUH RUQUV R[QZV RhWkWhW R\\XSZ',
	    'PoTJ[J R_JjJ RRO[O R_PlP RWJWY R_D_[ R_DjDkJ RWD[H RWERJ RTRTW RZQZV RdZ]Z R[XRY RlWk[iZ RfJeOgX',
	    'PoTJ\\J RSO]O RXJXY RYE]H RYCRJ RTRUW R\\R[V RfShYaY R]XRZ RiDiKiNk[ R`E`Q^[ RdEdRcZ RkD`E',
	    'PoTJ\\J RSO]O R_SfS Re[j[ RXJXY R_J_U R_JfJfS R]EkEj[ RXC]H RVFRJ RWCUH RTRUV R\\Q[V R]XRY',
	    'PoTJ\\J RaKiK RSO]O RfXjX RXJXY R\\Q[V RcPc[ R^PjPjX RXD]H RdElK RXDRJ RdE]K RURUV R]XRY',
	    'Po^ElE RTJ[J RSO\\O Rh[k[ RTQTV RWJWY RZQZV R^J^[ R^JkJk[ RWC\\H RhQiT ReEdNhQ RVFRJ RWCTH RaT_T R\\XRY RdHcR',
	    'PoTJ\\J R]MjM RSO\\O R_ZjZ RXJXY R_Q_[ R_QjQj[ RXC]H RWERJ RiJkO RWCUG RcC_M R[Q[V R]XSZ RTQUV',
	    'Po^JlJ RTJ\\J RSO]O R_ZjZ RXJXY R_Q_[ ReCeQ R_QjQk[ RXC]H RWERJ R\\Q[V RURUV R]XRY',
	    'Po^JlJ RTJ\\J RSO\\O RXJXY RfUk[ RXC]H RfVa[ RWERJ RbCbNgW RfDjF RiOdX RWCUG R`S][ RbJeT RiMfV R[Q[V RaN^Y R]XRZ RTQUV RbI_W',
	    'PoTJ\\J RSO\\O RXJXY ReCeN RXC\\H RWERJ RaNdUhYl[ R_HkHjL R[Q[V R_OjNgTcY_[ RTQUV R]XSZ R_G_R]Z',
	    'Po_FlF RTJ\\J R_OkO RSO]O R][l[ RXJXZ ReFe[ RXC]H RWFRJ RhRjX RWCUH R\\R[W R]YRZ RURUW',
	    'PoTJ]J R`LkL RSO^O R`ZlZ RXJXY R`E`[ R`EkEkL R`RlRl[ RYCRJ RXE]G R\\R[W R^XRZ RTRUW',
	    'Po`IlI RTJ\\J RdNkN RSO]O RdTkT RXJXY RdHd[ RXC]H RWCVFRJ RTRUV RbD^M R\\Q[V R]XRZ',
	    'Po^GlG RTJ[J RRO[O RhXkX RWJWY RaLaX RfJf[ R`NkNkX RXD\\H RWDRJ RTRUW RcF^Q R[RYV RcDaN R[XRZ',
	    'PoTJ[J RSO\\O R_QlQ R_YlY RTRUW RXJXZ R[RZW R_I_[ ReCeY R_IlIl[ RXD\\H RVFRJ RWDUH R\\YRZ',
	    'PoTJ\\J RSO^O R`OlO R`ZlZ RXJXY R`E`[ RfEfZ R`ElEl[ RYC^H RWERJ R\\Q\\V R^XRZ RTRUW',
	    'PoUJ\\J R_KlK RSO]O R_RlR RXJXY R_E_S ReEe[ R_ElElS RYC]H RWERJ R\\Q[V R]XRY RUQUV',
	    'PoTJ\\J RRO]O RXJXY R[Q[V RXC]H RfElL ReJ`P RVFRJ RbG]L RWCUH RdCbH RgOcS`T RkSfX_[ RTRUV R]XRY',
	    'PoTJ[J RSO\\O R^PkP RXJXY R^H^P R^HkHkP RXC\\H RVFRJ RaTdXj[ RWCUH RcV`Y\\[ RTRUV R[Q[V R\\XRY RdCdQcW',
	    'Po`HkH RTJ\\J RSO\\O R^PlP RXJXY RgVl[ RXC]H RcU][ RWFRJ ReHeQiY RdQaW RWCUG RTRUV ReNgV R[Q[V RaC^L R]XRZ RdLcU',
	    'Po_IkI RTJ\\J RSO\\O R]PlP RXJXY RWC\\H RWCVFRJ RTQUV R[Q[V R`D^M R]XRZ ReDeObU^Z ReOgVk[',
	    'Po_ElE RUJ]J RSO^O RaTjT R^[l[ RYJYZ RaEa[ RaLjLjT RYC^H RWERJ R\\R[W RURUW R^YRZ',
	    'PodIlI RTJ\\J RSO]O R_ZkZ RXJXY R_Q_[ RdCdQ R_QkQk[ RXC]H RWFRJ R\\Q[V RTRUV R]XRZ',
	    'Po`HlH RTJ\\J RSO\\O RhVjV Ra[k[ RTRUW RXJXY RaKa[ ReIeW RkLkU RXC]H RlWl[ RWFRJ RWCUH RaC]L RjL^R R[Q[V R]XRZ',
	    'PoUJ\\J RSO]O Rb[k[ RXJXZ R_H_L RbKb[ ReCeG R_HlHlL RXC]H RlXk[ RWFRJ RTRUW RWCVH RjObR R\\R[W R]YRZ',
	    'Po^HlH RUJ\\J RSO]O R^ZkZ RXJXY RXC]H R_NdScV_Z RWERJ RiVl[ RhLfR RWCUG ReCdHaO R[Q[V RTQUV R]XSZ',
	    'Po_ElE RUJ\\J RgNkN RSO]O R_ZkZ RXJXY R\\Q[V R_Q_[ R_QkQk[ RXC]H RcJ_N RWERJ RdEbL RUQUV R]XSZ RlFkN',
	    'PoTJ\\J RSO\\O R`RgR R`[k[ RXJXY R[Q[V R`K`[ R`KgKgR RlWk[ RXC]H RWFRJ RWCUH RaC]M RjUfU R]XRZ RTRUW R`HkHkOjU',
	    'PoTJ\\J R_LkL RSO]O RXJXY RfEf[ RZE]I RhSlV RaS^V RYDRJ ReKiT R\\R[V ReKaT RTRUW RjD_F R]XRY',
	    'PoTJ[J RSO\\O Rb[i[ RWJWY R[QZV RbIb[ RiXi[ RWC\\H RdT\\[ RVFRJ RaDeE RjGcT RWCTH RjMlU RjEgO RTRTW R^N]V R\\XRZ',
	    'Po_JkJ RTJ\\J RSO]O R_RkR R][l[ RXJXZ R[R[W ReDe[ RXC]H RVFRJ RWCUH R`E^N RTRUW R]YS[',
	    'PoUK\\K R`LjL RSO^O R`SjS R][l[ RXKXZ R`E`[ R`EjEj[ RYD^I RWFRK R\\R\\W RURUW R^YR[',
	    'Po^ElE RTJ\\J RSO]O RbYkY RXJXY RbMb[ RaOkOk[ RXC]H RWERJ R\\Q[V RcI^T RTRUV RdEaQ R^XRZ',
	    'PoUJ\\J RSO]O R`QlQ R`YlY RXJXZ R\\R[W R`H`[ R`HlHl[ RYD]H RWERJ ReCdH R^XRZ RURUW',
	    'Po^IlI RTJ[J RRO]O R^RkR R\\[l[ RTRUW RXJXZ ReIe[ RXC\\H RVFRJ RaDgF RWCUH R[R[W R]YRZ',
	    'PoaHlH RTJ[J RRO\\O RiXkX RXJXX R`M`W RfGf[ R`MkMkW RXD\\I RXDRJ RTRUV RbC`I R[QZV R\\WRY',
	    'Po^ElE RTK[K RRP[P RhUkU R^[l[ RWKWZ RZRZW R^E^[ RaKaU RfGfX RaKkKkT RWD\\I RWERJ RTSTW R[YR[',
	    'Po_ElE RTK[K RRP\\P RcUjU R_[l[ RXKXZ R_E_[ RcKcU RcKjKjU RXD\\I RWERJ R[RZW RTSTX R\\YR[',
	    'PoTJ[J RSO\\O RXJXY RcRfV RXC\\H RVFRJ RaG]K R`GbKgPlR RWCUH RbC_I RaGiGgKbP]R R[RZW RjX_[ RTRTW R]XRZ',
	    'Po^IlI RTJ\\J RSO]O R`QiQ R`ZiZ RXJXY R`C`[ RiCi[ RXC]H RWERJ RWCUG R[Q[V R]XSZ RTQUV',
	    'PoTJZJ R^MkM RRO[O RWJWY RZQZV RgCg[ R^GkGkL RWC[H RaW][ RUGRJ RVCTI RkXiX RTQTV R\\YRZ R^L]RlQkX RbCbRaX',
	    'PoTJ\\J RSO\\O R_RjR RXJXY R_E_R R_EjEjR RXC]H RaW][ RWCVFRJ RgUjWl[ RTRUW R[Q[V R]XRZ',
	    'PoTJ[J RSO\\O RiVlV RXJXY R[QZV R_F_U RgFg[ RgFlFlV RXC\\H RbW^[ RVFRJ RWCUH ReD_F RTQUV RdHdT]U R\\XRY RdOcW',
	    'PoTJ\\J RSO]O R`ZjZ RXJXY R`Q`[ R`QjQj[ RXC]H RXCRJ RgElN R\\Q[V RTRUV RbD`M R]XRZ',
	    'PoTJZJ RbJlJ RRO[O Re[i[ RWJWY RZQZV R`G`[ RiCi[ RWC[H RcOeT RVCTI RaC\\M RTQTV R\\XRY',
	    'Po]JkJ RTJ[J RRO\\O R\\[l[ RXJXZ RdCdI RWC\\H RVFRJ RWCTH R[RZW RiMgY R]YR[ RTRUW R`MaX',
	    'Po`IlI RTJ\\J RSO\\O RXJXY R[Q[V R`I`V RXC]H RhDkF RgTaZ RWCVFRJ RjMfV RdT]V RTQUV R]XRZ RfCfKfNhY RlWk[i[',
	    'Po^ElE RTK[K RRP\\P R][l[ RXKXZ ReIeX RXD\\I RgKlP ReJ^Q RWERJ R[RZW RfEdK RTRTW R\\YR[',
	    'PoTJ[J RSO\\O R]QlQ RXJXY R_H_Q R_HjHjQ RgWl[ RXC\\H RWFRJ RdCeRhY RWCUG RdQbV_Y\\[ RTQUV RdOfV R[Q[V R\\XRZ RdMcU',
	    'PoTJ\\J R^LkL RSO\\O R]RlR RXJXY ReCe[ RXC\\H RiEkJ RVFRJ RWCUH RaE^J RTRUV R[Q[V R\\XRZ',
	    'Po]JlJ RTJ\\J RSO\\O R`VhV RXJXY R[Q[V ReCe[ RXD\\H RXDVGRJ ReJjU ReJ_T RTQUV R]XRZ',
	    'Po_IkI RTJ[J RRO\\O R^PlP RWJWY RWD\\H RWERJ RTRTW RiVk[ RZRZV ReCdP_Z R\\XRZ RdLdS RfY^Z RjYdY',
	    'PoTJ\\J RSO]O R`UgU Re[j[ RXJXY R`M`V R`MgMgU RXC]H RWERJ RcC^M R\\Q[V RTRUV R^XRY RaIlHlSj[',
	    'Po^IlI RTJ\\J RSO]O RhXlX RXJXY R[Q[V RdCd[ RiLiX RlUlX RXC]H RhDjF RWERJ RWCUH RTQUV R]XRZ R`L`R_X',
	    'Po^ElE RUJ\\J RSO]O RePkP R][l[ RTRUW RXJXZ R`L`[ ReEe[ RXC]H RWFRJ RWCVG R\\R[W R]YRZ',
	    'PoTJ\\J RSOlO R^WlW Re[h[ RXJXY R[Q[V RcQfT RXC]H RcHfK RWERJ RWCUG RTQUV R`E`K`N_R^W R]XRZ R`FjFjTi[',
	    'PoTJ\\J RSO]O RXJXY R\\Q[V RXC]H RWERJ R`HkHjL RWCUH RaE^M RiNkV RaN^V RTQUV R]XSZ ReHe[a[',
	    'PoTJ[J RSO[O Ra[d[ RWJWY RZQZV RdKd[ RWC\\H RfFlL ReCfG RUFRJ RbG\\L RVCTI RcCaH RTQTV RiOkW R`O]W R[XRZ',
	    'PoTK[K R]KlK RRP\\P RdUiU R`[l[ RXKXZ R`D`[ RdCdU RiDiU RXD\\I RWERJ RZRZW RTSTX R\\YR[',
	    'PoTJZJ RRO[O RfXlX R_[b[ RWJWY R\\JdJdY RfFf[ RfFlFlZ RWD[H RVFRJ RTRTW RWCUH RYRYV R[XRY R_C_O]X',
	    'PoTJ\\J R`KlK RRO]O R`RlR R`YlY RXJXY R`E`[ R`ElEl[ RXDZF]I RXDRJ R[R[W R]YRZ RTRTW',
	    'PoTK\\K R`MjM RSP]P R`UjU R^[l[ RXKXZ R`E`U R`EjEjU RYE]I RYDRJ R\\R[W RTSUW R]YRZ',
	    'PoTJ\\J R_NlN RSO\\O R_TlT RXJXY R[QZV R_H_U ReCe[ R_HlHlU RXC\\H RVFRJ RWCUH RTQUV R\\XSZ',
	    'PoTJ[J RSO\\O RWJWY R_E_V RjWl[ RgUkX RWC\\H RcHeM RVCTI ReR]V RiPfVcY^[ RTQTV RZQZV R\\XSZ RjDjJhS',
	    'Po^IiI RTJ\\J RSO\\O R`UgU Rh[l[ RXJXY R[QZV R`N`W R`NgNgU R^DlDl[ RXC\\H RVFRJ RWCUH RTQUV R\\XRY',
	    'Po_HlH RTJ\\J RSO\\O RbYkX RXJXY R[Q[V ReCeH RXC]H RXCRJ RhSl[ RTQUV ReKbY R\\XRY R_H_Q][',
	    'PodGkG RTJ\\J R_NlN RSO]O RXJXY RdCd[ RYE]H RgSkV RYDRJ R[R[W R]XRZ RTRTW',
	    'PoTJ[J R]KbK RRO\\O Rf[k[ RXJXY RbDb[ RgDg[ RkVk[ RXD\\I RlJfO RWERJ R[RZW RbU^V RTRTW R\\XRZ',
	    'PoaGlG RYQeQ RTUjU RS[l[ RUEUL R[C[L R_Q_[ RaF^J RYWYZ RcJgK RfVd[ RbD`H R_LdOlQ R_LYORQ',
	    'PoTJ[J RSO\\O R^RjR RWJWY RZQZV R^I^R RdCdY R^IjIkR RWD\\H RVGRJ RiUl[ RVDTI RTRTW R\\XRZ RhY^Z RjX`Z',
	    'PoTJ[J RSO[O R]OfO Ri[l[ RXJXY R[QZV R]EeEd[ RhFhU RlDl[ RXC\\H RVFRJ RTQUV RWCUH R\\XSZ R_E_R^Y',
	    'Po^JkJ RTJ\\J RSO]O R]SlS RXJXY R[Q[V RbCbS RhChS RhVkY RXC]H RaW][ RWERJ RWCUG RTRUV R]XRZ',
	    'Po^GlG RTJ\\J RSO\\O RXJXY ReCeG RXC]H RgJlN RWERJ RaK^N RaOdUhYl[ RhNfTaY\\[ RTQUV R[Q[V R]XRZ',
	    'PoTJ[J RSO\\O RcQlQ RXJXY RZQZV RXC\\H R`HdJ RWCUGRJ ReC_I RbGkGeM`SeU RgK^O RbR]T RTQUV RkRfWbZ][ R\\XRY',
	    'PoTJ[J RaKjK RRO\\O RaQjQ RXJXY RaEaW R_EkEj[ RXD\\I RXDRJ RTRTV R[QZV R\\XRZ RjV^X RlV`W',
	    'PoTJ[J RSO\\O R_ZjZ RWJWY RZQZV R_Q_[ R_RjRj[ RWC\\H RUFRJ R`G\\K R`GbKfOlR RVCTH RaC_I R`FiFgKaP\\R RTQTV R\\XRY',
	    'Po_IlI RTJ[J RSO\\O R^PlP RXJXY RhIh[ RXC\\H RVFRJ R`CbH RWCUH R[QZV RjChH RTRUV R]XRZ RbIbSaX',
	    'Po^IlI RTJ[J R^NeN RSO\\O RXJXY RaNaX RXC\\H RiDkF RVFRJ RlXj[k[ RWCUH RTQUV R[QZV RfW]Y RgCgNgQiZ R\\XRY',
	    'Po^JkJ RTJ[J RSO\\O R_PkP R]UlU RXJXY R[Q[V ReLe[ RXC\\H RhFlJ RWFRJ RWCUG RcC`J RaM^S RTQUV R]XRZ',
	    'PoTJ\\J R`MiM RSO]O R_ZjZ RXJXY R_R_[ R_RjRj[ RXC]H RfFlL ReCgH RWERJ RbG]L RTRUV RcCaI R\\Q[V R^XRY',
	    'Po`JjJ RTJ\\J RSO]O R`OjO RXJXY R`D`Z R`DjDjO RXC]H RXCRJ RjQgU R\\Q[V RTRUV R]XRZ RfY]Z RdPgWk[',
	    'PoTJ\\J RSO]O R`QgQ R`VgV Re[j[ RXJXY R`L`W R`LgLgV RYE]I RXDRJ RbC^L R[R[W RTRUW R]XRZ R`HlHlSj[',
	    'PoTJZJ RRO[O RWJWY RfDf[ RlDl[ RWD[H RWERJ RhKjQ RZRZV RTRTV RcKcQ R^K]Q R[XRZ R`D`R_Y',
	    'Po^GlG RTJ\\J R^NkN RSO\\O Rg[l[ RXJXY ReCeG RgPg[ RlWl[ RWC\\H RgJlN RWCRJ RTRUV RcG`N R[Q[V R\\XRY RaPaW\\[',
	    'PoTJ[J RRO\\O RcUhU Rh[l[ RXJXY R_H_[ RcMcV RcMhMhU R_HlHl[ RXD\\I RXDRJ RTRUW R[RZV ReCcH R\\XRZ',
	    'PobIiI RUJ\\J RSO]O RcTiT Rh[l[ RXJXY R\\Q[V R_E_[ RcNcV RcNiNiT R_ElEl[ RXC]H RXCRJ RUQUV R]XSZ',
	    'Po]ElE RTJ[J RRO\\O R_RkR R_YkY RXJXY R_K_[ R_KkKk[ RXD\\H RXDRJ RTRTW R[RZW RdEcK R\\XRZ',
	    'Po^GjG RTJ[J R^KjK RRO[O R^PkP RXJXX RdCd[ RXD\\H RcPlX RbS]X RWERJ RdOaT RTRTW RZR[WRZ',
	    'Po_HkH RUJ\\J R^NlN RSO]O R_TkT R][l[ RXJXZ ReCeN ReOe[ RXC]H RVFRJ RWCUH R[R[W R]YRZ RURUW',
	    'PoTJ\\J R]JlJ RSO\\O R_OjO R^SjS R]WlW RXJXY R[QZV RdCd[ R_FjFjO RXC\\H RVFRJ RWCUH RTQUV R\\XRY',
	    'Po^IlI RTJ[J RSO\\O R^QdQ RWJWY RZQZV RWC\\H RhDkF RgTa[ RjMfV RVCTI RTQTV R\\XSZ RfCfKfNhY R^I^S]Z RlWk[i[',
	    'PoTJ[J RSO[O R`OlO RaZkZ RWJWY RZQZV R_G_[ RgFgZ RWC[G RUGRJ RTRUV RVCTI R`C[O RkDaG R[XSZ R_D^K',
	    'Po]HlH RTJ[J RRO[O RbSlS Rd[g[ RWJWY R`L`[ RhOh[ RWD[H RdLkLgP RWERJ RTRTW RZRZW RcE_O R\\XRZ',
	    'Po^ElE RTJ\\J R_NkN RSO]O R_TkT R][l[ RTRUW RXJXZ R[R[W ReOe[ RXC]H RVFRJ RhIjKkO RWCUH RdE`M R]YR[',
	    'PoSE\\E RhNkN RUViV RR[l[ RXEXL R_R_[ R`DhDhM RlJlM RYWZZ RbHeJ ReWdZ R_NiR R^NTS RZRcQlS R_J]LRM R`E_K',
	    'PoTJYJ R[NlN RROZO R`[b[ RSRTW RWJWY R^DbDb[ RVDZH RVCTI RYRXV RZXRZ RfDjDj[h[ R^D^S\\Z RfDfTd[',
	    'PoSDaD RfPjP RZSdS RVVhV RS[k[ R_S_[ RdEdM RjCjO RYX[[ ReXc[ RWH_HZN RWJ[LTP RZDVHRJ R_ObPlS R_NYQRS',
	    'Po`IkI RTJ[J RSO\\O R^OlO Rg[l[ RXJXY ReCeO RgOg[ RlXl[ RXC\\H R`W\\[ RVFRJ RWCUH RaE^L R[Q[V RbO`X R]XRZ RTRTW',
	    'Po]EcE ReEkE RTJ[J RSO\\O RdOlO RWJWY RZQZV RhEh[ RWC\\H RUGRJ RVCTH RTQTV RcN\\P R\\XSZ R`E`Q_X',
	    'PoTJ\\J R_NjN RSO]O R_TjT R][l[ RXJXZ RdNd[ RXC]H RfGlM RVFRJ RcH]M R_EeEgI RWCUH RdDbI R\\R[W R]YRZ RTRUW',
	    'Po_ElE RTJ[J RRO\\O R^TlT RXJXY RfEfN RePe[ RXD\\H RgHkL RXDRJ RfE_M RTRUW R[RZV R\\XRZ',
	    'Po]FlF RTJ\\J R_NkN RSO\\O RXJXY R^JkJkN RfVk[ RXC]H RWERJ RdCdRhY RdRbW`Y\\[ RTQUV R[Q[V R^N^SkRkViV R]XSZ RdMcV',
	    'Po_IkI RTJ\\J RSO\\O R^OlO RXJXY R[Q[V RdCd[ RhUlY RXC]H RWCVGRJ RfOiW RTQUV R`D^L RcO`V\\YRZ',
	    'PoTJ[J R_KlK RSO\\O RaZkZ RXJXY RaQa[ RaQkQk[ RXC\\H RVFRJ RWCUH RTRUV R[QZV R\\XRY RkD_F_R]Z',
	    'PoTJ\\J RSO]O RcZlZ RXJXY RcQc[ RcRlRl[ RXC]H R`JcKfN RXCRJ RTRUV R\\Q[V RcGkFhMbR^T R^XRY RdDcG_K',
	    'Po]GlG RTJ[J RaMhM RSO\\O R^PkP Rc[g[ RXJXY R[QZV RXC\\H RgJlN RWCUGRJ RaJ]N RfGhL RdC`L RTQUV R`UiUg[ RaP`V R\\XRY',
	    'PoTJ\\J RSO\\O Rg[l[ RXJXY R\\Q[V RgCg[ RlWl[ RXC\\H RgQkT R`Q^T RbP_S RaW\\[ RWFRJ R^H`M RjHhL RWCUG RTRUV R]XRZ RbCbQaW',
	    'PoUJ\\J R^LlL RSO]O R`ZkZ RXJXY R`R`[ RfEfR R`RkRk[ RXC]H RWERJ R\\Q[V RURUV RjD_F R]XRY',
	    'PoeElE R\\JcJ ReLlL RZOdO Re[i[ RWLW[ R[Q\\V R_J_Y RiLi[ R_CdH RXCSI RWKRP R]FYJ R^C\\H RcQbV RdXYY',
	    'PobJlJ RTJ[J RRO[O RWJWY R`G`[ RgCg[ RWD[H RWERJ RTRTW RaF]N RZRYV ReOaW RgHkV RaC^M RfHcU R[XRY',
	    'PoWKdK RWNdOhO RZSdS RVVhV RS[k[ RWHWN R_S_[ RWHdHdN RYX[[ ReXc[ RWDSH R_OcRlT R_OYRRT RVFiFhO',
	    'PoUJ\\J R_KlK RSO]O RiYlY RXJXY R\\Q[V R_P_Y R_F_K ReCeK ReMe[ RlFlK R_PlPlX RYC]H RWERJ R]XRY RURUV',
	    'Po^GhG RTJ[J R]MlM RSO\\O Ra[k[ RTRTW RWJWY R[QZV RaPa[ RcCcM RWC\\H RaQ\\S RjEhI`R RkXk[ RVFRJ RVCTH RiRaU R\\XRZ',
	    'Po^GlG RTJ[J R_NkN RRO\\O R_ZkZ RXJXY R_R_[ ReCeM R_RkRk[ RXD\\H RWERJ RTRTV R[RZV R\\XRZ',
	    'Po`FiF RTJZJ R^KkK RSO[O Ra[d[ RWJWY ReKe[ RWD[H RfRlX ReOgT RjOfS RWERJ RZRYV R^PbP_W RTRTW R[XRY',
	    'PoTJZJ RRO[O RfYlY RTRTW RWJWY RfFf[ RfFlFl[ R^RdX RWD[H RWCUGRJ RaU\\[ RcP_Y RYRYW R[XRY R`C]R R]IdIaV',
	    'PoTJ[J R]NlN RSO\\O RXJXY R[QZV RdCd[ RhTlX RXC\\H RWCUGRJ RdLhU RjEhK R_E`K RdL`V\\XSY RTQUV',
	    'Po]HlH RTJ[J RSO\\O RXJXY RaNaZ RhWl[ RXC\\G RbL]Q RVFRJ RkLfQiY RWCUH ReCdI`O RTQUV ReLhW RZQZV RfX][ R\\XRZ RdFfT',
	    'PoTJ[J RaLjL RRO\\O R_ZlZ RXJXY R_D_[ R_DlDl[ RXD\\H RXDRJ RfGeOjW ReObV R[RZV RTRUV R\\XRZ ReJdT',
	    'PoTJ[J R]NlN RSO\\O Rg[l[ RXJXY RdCdN RgNg[ RlWl[ RXC\\H R`W\\[ RVFRJ R^FaK RkEhK RWCUH RTRUV R[Q[V R]XRZ RbN`W',
	    'PoTJ[J R]NlN RSO\\O RWJWY R[QZV R^G^K RdCdG R^GkGkK RWC\\H RVFRJ R`TkZ RWCTH RiPfVdX][ RTQTV RcI_V R\\XRY',
	    'PoTK[K R^KkK RSO\\O R^QlQ RXKXY ReEeQ RXD\\I RXDRJ RhTiWk[ R[R[W RcQ^Z RTRUW R\\XRZ RjD^F RjY^Z',
	    'Po^GlG RTJ[J RRO\\O RaPjP RaUjU Rf[j[ RWJWY RaJa[ R`MjLj[ RXD\\H RWDRJ RTRTW R[RZW RcF^O RcC`L R\\XRZ',
	    'PoTJ\\J RSO]O R_SkS R_[k[ RXJXY R[Q[V R_J_Z ReKeR R^LkLkT RXC]H RlXk[ RWFRJ RWCUH RbC^M RaGhGeM RTRUW R\\XRZ',
	    'Po_GiG RTJ[J R^LlL RRO\\O RbPkP Re[i[ RXJXY RdCdL RXD\\I RWDRJ RTRTV R[RZV RkEjIbP\\S RcOaUjUi[ R\\XRZ',
	    'Po^IjH RTJ\\J R_MiL RSO\\O R`QiP RXJXY R[Q[V RXC]H RXCVFRJ RfDjF RjRfW][ RTQUV R]XRZ RcCcLfZk[lW',
	    'PoSFkF RZRdR RVVhV RS[k[ R\\F\\M R_R_[ RbFbM RYWZZ R_C`F RfIjM RXITM ReWd[ R_NdPlR R`MYPRR',
	    'PoTJ[J RSO\\O R^PkP RXJXY RePe[ RXC]H R_HdL RjMlQ RhLkO RhGfJaP RVFRJ RbFaI RhSkY RWCUH RdCbH RaS^Y RTQUV R\\Q[V R\\XRZ',
	    'Po]GlG RTJ[J RSO\\O RXJXY RaCaJ RhChJ RXD\\H RXDRJ R_LcThYlZ R[R[W RiLfS`Y\\[ R\\XRY RTRUW',
	    'PoUJ\\J R`KkK RSO]O RiYlY RXJXY R_P_Y R`E`K RfKf[ R`EkEkK R_PlPlY RZE]I RYCRJ R\\R[W RURUW R]XRZ',
	    'Po_HkH RUJ\\J R_MkM RSO]O R_PkP R^UlU RXJXY R\\Q[V R_D_M RePe[ R_DkDkM RXC]H RWERJ RTQUV R]XSZ',
	    'PoTJ\\J RSO]O Rg[l[ RXJXY R_K_QhQg[ R_KjKjQ RlXl[ RXC]H RaW][ RVFRJ RbDhDkK RWCUH R`F^K R[Q[V RbR`Y R]XRY RTRUW',
	    'PoSF]F R`JkJ RTOXO RZSdS RVVhV RS[j[ RXCXN R_S_[ RgIgO RYX[[ RdXc[ R_K]N R^OaQlT R_OYRRT RiD`F_L R\\IRJ',
	    'PoTJZJ R^JiJ R^NiN RSO[O R\\RiR Rd[i[ RWJWY R^F^R R^FiFi[ RWD[H RWDRJ RcCbF RTRTW RZRZV RlOfU[Z R[XRY',
	    'Po]HkH RTJ[J RSO[O R`[d[ RTRTV RWJWY RZQZV RdCd[ RgSlX RWC\\H RaS^V RjMgP RUGRJ R^L`P RdKhU RgDkF RVCTH RdHfS R[XRZ',
	    'PoTJ\\J RSO]O R`QjQ R`UjU RXJXY R`L`[ ReCeL RXC]H RhEkI RVFRJ RTRUV RWCUH R`E^J R[Q[V R`LjLj[f[ R]XRY',
	    'Po\\ElE RTJZJ R^JcJ RSO[O R^PcP Rg[l[ RTQUV RWJWY RZQYV R^E^W RcEc[ RhEhZ RWC[G RUFRJ RlVl[ RVCTI RcU\\V R[XRY',
	    'Po]IlI RTJ[J RSO\\O Re[i[ RXJXY ReEeO RXC\\H ReIkO R`W\\[ RVFRJ RWCUH RTQUV R[QZV RbQ`X RcJ^PgQfUkUj[ RiD^E R\\XSZ',
	    'PoUG[G R]GlG RSJ[J R]MkM RZSdS RUViV RS[k[ RUDUH R[C[N R_S_[ RdCdM RZW[Z RdWcZ R_OdQlS RUJTO R_OYRRT',
	    'PoUJ[J R_MkM RSO\\O RhYkY RXJXY ReHe[ R_HkHkM RXC]H RiChH RePcT]Z RVFRJ RaDbH RWCUH R[Q[V R\\XSZ RTQUV R_M^RlRkY',
	    'PoTJ[J RSO[O R^TkT R\\[l[ RTRUW RXJXZ R[RZW RdCd[ RXC\\H R`JaN RVGRJ RiJkP RWCUH RiGfQ R`F^P R\\YR[',
	    'Po_JlJ RTJ\\J RSO\\O R_ZjZ RXJXY R_E_P R_R_[ R_RjRj[ RXC\\H RVFRJ RTRUV RWCUH R[Q[V RlNlPiP ReDgN ReN]O R]XRZ RiC_E',
	    'Po_EjE RTJ[J R]KlK RRO\\O Ra[d[ RXJXY RdEdK RdMd[ RgTlX RXD\\I RjOgR RWERJ RdMhV R[RZW R]PbP^X RTRTW R[XRZ',
	    'PoUJ\\J RSO\\O R_PlP R_UlU Ri[l[ RXJXY R_K_[ RfKf[ R_KlKl[ RXC]H RWERJ RbGhJ RWCUG R_ElEiGeH R[Q[V RTQUV R]XSZ',
	    'Po^ElE RTJ\\J RSO]O R_QjQ R][l[ RTRUW RXJXZ R_J_Q R_JjJjQ RXC]H RWFRJ RWCUH RaSbY RhSgZ R[R[W R]XRZ',
	    'PoTJ\\J R`LkL RSO]O R_ZlZ RXJXY R_Q_[ R`D`L R`DkDkL R_QlQl[ RXC]H RWERJ R\\Q[V RTRUV RfLdQ R]XRY',
	    'Po`GlG RUJ\\J RSO]O R]QlQ R_XlX Re[h[ RXJXY R[Q[V RcSeV RXC]H RcMeO RWERJ RaC^K RTQUV R]XSZ R`KjJjVi[ R`K`P_T_Y',
	    'PoTJ\\J R_KjK RSO]O R_PjP RXJXY R_F_Z RdCdF R_FjFjP RfVk[ RXC]H RjTfU RVFRJ RdPgX RWCUH R[QZV RTRUV R]XRY RfY]Z',
	    'Po^EkE RTJ[J RRO\\O R^RjR RXJXY RXD\\I RdJgKkO RdJ_O RjGiJ RWERJ R`SdXl[ R`G_K RiSeX][ RTRTW RZRZW ReEdK R\\XRZ',
	    'Po_GlG RTJ\\J R^NlN RSO]O R_TlT RXJXY ReCeG ReNe[ RXC]H RVFRJ RaIbM RiIhM RWCUH RTRUV R\\Q[V R]XRY',
	    'PoRH[H R_IgI R^MhM RePiP RVVhV RR[l[ RWCWO R_S_[ RZX[Z RdWcZ R_OiS R_C]I RZScRlT R[NRO R_OWSRT R_FkGiP',
	    'Po_JjJ RTJ\\J RSO\\O R_OjO R_UjU RXJXY R[Q[V R_D_U R_DjDjU RgWl[ RXC]H RbW^Z RWCVFRJ RTQUV R]XRZ',
	    'PoYIlI RZTdT RVWhW RS[k[ R_T_[ RZXZZ RdXc[ RTDWE RcJfMkO RRHVI RWJUO RaJ_MYO R_OeSlT R_PWTRU RZEbEaK RhD`E',
	    'Po]GkG RTJ[J RRO\\O R^QjQ RWJWY R^K^Q RdCd[ R^KjKkQ RfSlY RWD\\H RWERJ RTRTV ReQgU RZRZV RbS\\YRZ',
	    'PoTJ[J RRO\\O R`ZjZ RTRTV RXJXY R`R`[ R`RjRj[ RXD\\I RfLlQ RgDkH RcL^Q RWERJ RbF^J ReHgN R[RZV RbDaH RdHbN R\\XRZ',
	    'PoTJZJ RgMkM RSO[O RcVlV RbZlZ RWJWY RZQYV RdKdV RhEhV RWC\\G RVFRJ RWCUH R`S]Z RTQTV R]Q_X R\\E`E]NaM_X RkDbF R[XRZ',
	    'PoTJ[J RcMlM RSO[O RcVkV RcZlZ RTQTV RWJWY RZQZV RhEhV R_VcZ RWC\\H RVFRJ RWCUH R^Q_W R]E`E^N RkDcG RaM]M RaM_X R[XRZ',
	    'Po]FkF RTJ[J RSO\\O R]TlT RWJWY R]J]N RdCdJ R]JlJlN RWC\\H R_NiNfQ RUGRJ RVCTI RTQTV RZQZV R\\XRZ RdRd[`[',
	    'PoTJ[J R_KiK RRO\\O R^PlP Rg[l[ RXJXY R^F^J RdCdF RgPg[ R^FlFlJ RlXl[ RWC\\H RaW][ RVFRJ RWCTH R[QZV RbP`X R\\XRY RTRTW',
	    'Po^HlH RTJ[J RSO\\O RXJXY R[Q[V RfVl[ R_NbQ RXC\\H RhNlQ RbU\\[ RVFRJ ReCeQhY RWCUH RdQ`X RdNgW RiJgQ R`J^R RTQUV RdLeU RdMcU R\\XRZ',
	    'PoTJ[J R^MlM RSO\\O RaZjZ RXJXY R[QZV RdCdG RVFRJ RiTl[ RWCUH RaR][ RePbZ RXC]G^K R^GlGlK RbJ_X R\\XRY RTQUV',
	    'Po^IkI RTJ[J RSO\\O RXJXY R[QZV RXC\\H RhFkJ RbQ^U RVFRJ RaSfYl[ RWCUH RcCaI`N^O RgIgNkMkN RcOaSiSeX][ RTRUV R\\XRZ',
	    'PobFiF RTJ[J RSO\\O R_QjQ R_TjT R^WlW RXJXY ReNe[ RXC\\H RVFRJ RbE^J RaGfLlO RWCUH RbCaH RiGdL^O RTQUV R[QZV R\\XRY',
	    'PoTJ[J R]KfK RSO[O Rh[l[ RXJXY RZQZV RbEb[ RgGgU RlDl[ RaMeR RXC\\G RVFRJ RTQUV RWCUH RaK]U ReD\\F RaH`O R[XRY',
	    'PoTJ[J RRO[O R_RkR Re[l[ RWJWY R_J_R RfRf[ R^KkKkR RlXl[ RWD[I RWERJ R`H]K RTRTW RbC_I RZRYV RdRbW_Y\\[ R[XRZ RaFgFeLcU',
	    'Po^HkH RTJ[J R_NkN RSO\\O Rb[h[ RXJXY RcSc[ ReCeN RhXh[ RcPfS RXC\\H RVFRJ RiTlY RWCUH R[Q[V R_T^Z RTRUW R\\XRZ',
	    'PoTJ[J RRO\\O R^PlP RXJXY RXD\\I RWDRJ RTRTV R_H`L RdGeK R`UkZ RjGhL R[RZV RdM`U RiPgVdY^[ R\\XRZ RkDaE',
	    'Po`JkJ RUJ\\J RSO]O R_RlR R_VlV Rh[l[ RXJXY R\\Q[V R_M_[ R`D`J R`DkDkJ R_MlMl[ RYC]H RWERJ RUQUV R]XRY',
	    'PoTJ[J RbKjK RSO\\O R\\TlT RWJWY RZQZV RbDbK R^F^OiNiT RbDjDjK RWC\\H RUFRJ ReTfWhYl[ RVCTH RbV`Y\\[ RTQTV RdPbX R\\XRY',
	    'Po^DkD RTJ\\J RSO\\O R^OlO R_ZjZ RXJXY R[Q[V R_S_[ R_IiIiO R_SjSj[ RXC]H RWERJ RWCUG RcEaO R]XRZ RTQUV',
	    'Po`GkG RZSdS RVVhV RS[k[ RWFWP R[F[N R_S_[ RYX[[ ReXc[ RYDRJ R`F]J R`JkO RaC_I R_OcQlT RhHeK_NYRRT',
	    'PoTJ[J R_LkL RRO\\O R^VlV RWJWY R^P^W R_G_L ReCe[ R_GkGkL R^PlPlW RWD\\H RWERJ RTRTW RZRZV R\\XRZ',
	    'PoTJ[J R^LjL RSO\\O RdSkS Rd[l[ RWJWY RZQZV R^D^L ReLeZ R^DjDjL R`VcZ RWC\\H RUFRJ R_SaX RVCTH RTQTV R_Q]Z R[XRY',
	    'PoTJ[J RSO\\O R]PlP Rd[h[ RWJWY RZQZV RhMh[ R_ScW RWC\\H RVFRJ RiFkL RVCTH RTQTV RdGdL R_E]M R\\XRY RjD_E',
	    'Po^ElE RTK[K RRO[O R_TkT R][l[ RWKWY ReTe[ RWD[I RWERJ R^LaQ RcLfQ RhLjP RjGhL RaG^L ReGcL RTRTW RZRZW R[XRZ',
	    'Po\\HlH RTJ[J R_KiK RSO\\O R`RiR RXJXY R^O^R R_DjDjK R^OlOlR RXC\\H RVFRJ RbTeXl[ RWCUH RiSeX^[ RTQUV R[QZV R\\XRY',
	    'Po_EkE RaIjI RTJ[J R_NlN RSO\\O RXJXY R[QZV RbNbZ RhWl[ RXC\\H RkPhS RWCUGRJ RfRiY ReNhW RTQUV R\\XRY RgY_Z R_E^R]Z',
	    'PoTJ[J R`JhJ RSO\\O R^ZjZ RWJWY RZQZV R^T^[ R^TjTj[ RWC\\H RdClJ RUFRJ RdC]K R^NiMgS RVCTH RTQTV R\\XRY',
	    'PoTJZJ RSO[O R]SlS Ra[d[ RWJWY ReQe[ RXD[H R_MjMfP RXDRJ R_GaK RcGeK RjFhK RZRZW R[XRY RTRTW RjC`E',
	    'PoTJZJ R\\NdN RROZO R_[b[ RWJWY RYQYV R]E]N R]EcEb[ RfEf[ RVC[G RTQTV RVCSJ RiMlSlWhW RfElEiM RbM^V[XRY',
	    'PoTJ[J R^KcK RSO[O R^QcQ RYY^Y RWJWY RZQZV R^E^X RWC\\H RVFRJ RhCgRcZ RVCTH RTQUV Rk[g[ R\\YSZ R^EcEdW[X ReJlJlUk[ RgLfV',
	    'PoTJ\\J R_KjK RSO]O R^UkU R][l[ RTRUW RXJXZ R_D_K ReOe[ R_DjDjK RXC]H RWFRJ RWCUH R[R[W R]YR[ RjN^P',
	    'Po_JkJ RTJ[J RRO[O RbWhW Rg[k[ RWJWY RbQbX RbQhQhV R_DkDkJ R_NlNlY RWD\\H RWERJ RTRTW RZRZV R[XRY R_D_R]Y',
	    'Po_IjI RTJ\\J R_MjM RSO\\O R_RjR Rf[l[ RXJXY R_D_R RgRg[ R_DjDjR RlWl[ RXC]H R`W\\[ RVFRJ RWCUH RTRUV R[Q[V RaR`Y R]XRZ',
	    'PoTJZJ R\\LlL RRO[O Ri[k[ R\\[`[ RWJWY RYQYV R`E`[ RlWl[ RWC[G RiDkH RgVb[ RjOfX RVCSI RTQTV RcD\\E RdQ[S R[XRZ RfCfNhZ',
	    'Po^GlG RTJ\\J RSOlO Ra[l[ RXJXY RaNa[ RbCbJ ReKeN RhChJ RXC]H RVFRJ RWCUH R[Q[V R]XRZ RTRUW',
	    'Po^GlG RUJ\\J RSO]O R_PkP R_TkT RXJXY R[Q[V R_K_[ ReCe[ RXC]H RWERJ RhDlE RTQUV R_KkKk[h[ R]XRZ',
	    'PoRI`I RePjP RZSdS RVViV RS[k[ RSEZEZP R_S_[ RcEcN RjCjO R[K_L RYX[[ ReXc[ RYIRP RWI]K R_PaPlT R`OZRRT R^CUE',
	    'Po_DkD RSG\\G RTQXQ RZTdT RVWhW RR[l[ RXCXQ R_T_[ RZXZZ ReXcZ R`HeMkP R_HjIfMaO R^QaQlU R_PYSRU R_D_I^O R\\JRL',
	    'PoTJ\\J RSO]O R_PkP Rg[l[ RXJXY R_I_P RgPg[ R_IkIkP RlXl[ RXC]H RXCRJ RTRUV R`DbH R\\Q[V RiChH R]XRY RbQaW][',
	    'Po^HlH RTJ[J RSO[O RTQUV RXJXY RZQZV RWC\\H RiDkF RhUb[ RVFRJ RkNfW RlWk[ RWCUH R^NdNcV`V RgCgMgPhZ R[XRZ R^H^S\\Z',
	    'Po_IkI RTJ[J R^NlN RRO\\O R_ZkZ RXJXY R_R_[ RdCdN R_RkRk[ RXD\\H RWDRJ RTRTV RZRZV R`D^K R\\XRZ',
	    'Po^JkJ RTJ[J RSO\\O R^PkP R^UkU R\\[l[ RWJWZ RZRZW R^E^P RdEd[ R^EkEkP RWC\\H RUGRJ RWCTI RTRUW R\\YRZ',
	    'Po]IlI RTJYJ RROZO R_YcY RSRTW RVJVY RYRXV R]Q\\V R`P`Y RdVcY RWD[I RhDkF RVFRJ R`LaO RdPeT RWCTH R[XRY RlVk[ RgCgMiZ',
	    'PoTJ\\J R`KkK RSO\\O R]SlS RXJXY R`F`S RhKhS RgVl[ RXC]H RaV\\[ RWFRJ RTQUV R[Q[V RjD`F R\\XRZ',
	    'Po^GlG RTJ[J R_JkJ R^OlO RSO\\O R^RlR Re[i[ RXJXY RiOi[ RbUdW RWC\\H RWCRJ R[QZV RTRUV RSZ[Y RdCbOaS^W',
	    'Po^FkF RTJ[J RRO\\O R_QjQ Rf[l[ RXJXY R_K_Q RdCdJ RgQgZ R_KjKjQ RlXl[ RXD\\H RaW][ RXDRJ RTRUV R[QZV RbQ`Y R\\XRZ',
	    'Po]IlI RTJ[J RRO\\O Rg[l[ RWJWY ReEeO RaQgQhZ RlWl[ RXD\\H RWDRJ RTRTW RdIhMlN RZRZV RdI`M]N R_Z\\Z R\\XRZ RaR`X RjD`E',
	    'Po]GlG RTJZJ R^MiM RRO[O Ri[l[ RWJWY RcPcZ RiPi[ RlXl[ RWD[H RfJkN RWERJ RdGaJ]N RcCeG RZRZW R\\ZZ[ RSRTW R[XRZ R_Q^X',
	    'Po\\ElE RTJZJ RSO[O R^QcQ RWJWY RZQYV R^E^X RcEc[ RWC[G RUGRJ RTQUV RfQjZ RjQeZ RVCTI ReKhW RjLgX RcV\\W R^KcJkIhV R[XRY',
	    'Po_JkJ RUJ\\J R_OlO RSO]O RaZkZ RXJXY R\\Q[U R_D`Sa[ RfJfT RaTkTk[ R_DkDkJ RXC\\H RWERJ RTQUV R]XRZ R_M]Z',
	    'Po`DjD R`HiH RTJ[J R`LiL RSO\\OlP RXJXY R`D`Z ReUk[ RXC]H RjRgU RVFRJ RcPfW RWCUH R[QZV RTRUV R]XRZ ReY\\Z',
	    'PoTJ[J RSO[O RhUkU Rf[l[ RXJXY R[QZV R^G^J RdCdG RgLg[ RfLkLkT R^GkGkJ RlXk[ R^PbT RXC\\H RaV\\[ RVFRJ RWCUH RcQ`Y RTQUV R`J]S R`MdMaW R\\YRZ',
	    'PoSE^E RSL]L RVVhV RR[l[ RYEYH RYLYO RSH^H^K R_T_[ RZXZZ RdXcZ RbFcIgNlO RSESP^OiT R`EjEhIeM`N RZSRU R^PTU RZTcSlU',
	    'PoUJ\\J RaLjL RSO\\O RbViV Rg[l[ RXJXY R^E^[ RbObX ReLeV RiOiX R^ElEl[ RXC]H RXDRJ RbGcJ RhFgJ R[QZV RTQUV R\\XRY',
	    'PoeFlF RTJ[J R^NkN RRO\\O R^RkR R]VlV RXJXY R^J^R ReCeJ ReRe[ R^JkJkR RXC\\H RVFRJ RWCUH R[QZV R\\XRZ RTQUV',
	    'PoTJ[J RaLiL RRO\\O R_PiP R`WhW Rf[j[ RXJXY R`S`W RdLdW RhShW RXD\\H RXDRJ RbC^K R[RZW RbI`O RTRTW R\\XRZ R`HlHlUk[',
	    'PoUJ\\J RSO]O RbVhV Rg[k[ RXJXY R[Q[V R_L_[ RbPbW ReCeL RbPhPhV R_LkLk[ RXC]H RjFkJ RWERJ RaE_J RTQUV R]XRZ',
	    'PoTJZJ R`KhK RRO[O R^TjT Rh[j[ RWJWY RZQYV R^O^[ RbOb[ RfOf[ R^OjOj[ RWC[H ReElL RbF\\L RVCTI RcCaG RTQTV R[XRY',
	    'Po^IjI RTJ[J R^NjN RRO\\O R]RlR RXJXY R^D^N RdDd[ R^DjDkN ReSlY RXD\\H RcS]Z RWDRJ RTRTV RZRZV R\\XRY',
	    'Po_FkF RTJ\\J R]JlJ RSO\\O RXJXY R[Q[V RdCeJfOlNlO RXC\\H RbQ]V RWCVFRJ RaSeXl[ RbJ`O]O RTQUV RaSiSeXaZ\\[ R]XRZ',
	    'Po\\EkE RZSdS RVWhW RR[l[ R_S_[ RcCcH RZX[Z ReXb[ RaKjO RYKWMSN RTDXE R_KiN RSHWI R_PjS R\\IiHfKcM]N R_PRT R\\G[N',
	    'Po`JiJ RTJ\\J R]NlN RSO\\O Ra[d[ RXJXY ReNe[ R_QaS RgUkY RXC]H RkQhS RWERJ ReOhW RTQUV R[Q[V RaC`L RcV]XRZ RaFiFhN',
	    'PoRH^H RfPiP RZSdS RVVhV RS[k[ RYDYP R_S_[ RXH\\M RYX[[ ReXc[ RYHRO RbC_J RfFcM R_ObPlS R_NZQRS RaGkGjP R\\DSE',
	    'Po^FkF R^IkI RTJ\\J R]LlL RSO\\O R_TjT Rg[j[ RXJXY R_O_[ RdCdL RdOdT R_OjOj[ RXC\\H RVFRJ RWCUH RTRUV R[Q[V R\\XRZ',
	    'Po_GkG RTJ[J R^NlN RRO\\O R`ZjZ RWJWY R`R`[ RdCfG R`RjRj[ RWD\\H RWDRJ RTRTV RaHaM RiIgN RZQZV R\\XRY',
	    'Po_IjI RTJZJ R]NlN RRO[O RdWhW R`[j[ RWJWZ RZQYV RaRa[ R`RhRhW RWC[H RhOlS RhDlH R`O\\S RjXj[ RfIjR RVCTI R`D^I RcI_R RTRTW RdDaO R[YRZ',
	    'Po^IlI RTJ[J RRO\\O R_VjV R_ZjZ RWJWY R^E^O R_R_[ R_RjRj[ RlMlP RXD\\H RWDRJ RhNkP R[RZV RTRTW R^EdDgN RdN]N R[XRY RiCaD',
	    'Po^GlG RTJ[J RRO[O R]TlT RTRTW RWJWY RdQd[ RWD[H R`LcO RgLkO RWERJ RdCeG RhIeP RZRZV RaI^P R[XRZ',
	    'PoTJZJ R^KcK RfKlK RROZO R^QcQ RfQlQ R`[c[ Ri[l[ RWJWY R^DcDcZ RfDlDl[ RWD[H RVCUGRJ RTRTV RYQYV RZXRY R^D^R\\Y RfDfTe[',
	    'Po_IkI RTJZJ R^LlL RSO[O R`PjP R_UlU RWJWY R_E_I ReCeI RfPf[ RkEkI RWD\\H RWERJ RZRZV RTRUW R[XRY R^L^S]Y',
	    'PoTJ\\J R_KkK RSO]O R_PkP R]UlU RXJXY R[Q[V R_F_P RgRg[ R_FkFkP RXC]H ReFeP`S RWERJ RWCUH RdCdF R]XSZ RTQUV',
	    'PoTJ\\J RRO]O R_TdT RgTlT R_ZlZ RXJXY R`O`[ RgOlOl[ RXD\\H RXDRJ RcF^K RcGkGhK R[R[W RdCbH RdM_O RTRUW R\\XRY',
	    'Po^ElE RTJ\\J RSO]O RgSkS R][l[ RXJXZ R^L^SbSb[ RgSg[ RkLkS RXC]H RVFRJ RWCUH RgEgLkL R\\R[W RbEbL^L R]YRZ RTRUW',
	    'Po`IlI RTJ\\J R`NkN RSO]O R`TkT R`YlY RXJXY RaHa[ RfGfY RXC]H RVFRJ RWDUH RbC^M R[Q[V RTRUV RgCfI R]XRY',
	    'PoTJ[J R^MkM RSO\\O R^SkS RXJXY R^HkHkM RfVk[ RXC\\H RhPjQ RdW`[ RVFRJ ReRhY RWCUH R[QZV RTQUV ReOcX R]XSZ R^E^S]Z RiD^E',
	    'PoTJZJ RRO[O Rf[k[ RTRTV RWJWY R]E][ RdJdV RcElEk[ RWD[H RVFRJ RVCTH RYRYV R]EaE_LaRaU_U RZXRY RdJhJhTdT',
	    'Po^GkG RTK[K R_LjL RRO\\O R]QlQ Rf[k[ RXKXZ RfQfZ RkWk[ RXD\\I RWERJ RhDfG RaDbG RZRZW RTRTW RdGdPaW\\ZRZ RdJbU',
	    'PoTJ\\J RSO\\O RbOeO R`UjU R`ZjZ RXJXY R`Q`[ ReCeO R`QjQj[ RXC]H RgIkN ReEhK RkFgI RWFRJ RbJ^N RWCUG R_GcG`M R[Q[V R]XRZ RTQUV',
	    'Po_IkI RTJ\\J R_MkM RSO\\O R_SdS Rf[l[ RXJXY R[Q[V R_D_M R_O_Z RgOg[ R_DkDkM RlXl[ RXC]H RWFRJ RWCUH RkRfU R]XSZ RdZ]Z RTRUW',
	    'PoYE^E RRI`I RVPYP RZSdS RVVhV RS[k[ RYCYO R_S_[ R\\K^M RYX[[ RcIfJjO ReXc[ RUKSO RaEjEhJeMaN R_PaQlT R_OZRRT',
	    'Po^IlI RTJ[J RRO\\O R^SlS RXJXY ReEeN RXD\\H ReHiMlN ReH_N RWDRJ RTRTW RZRZV RcP`WjY RhSgWeY^[ R\\XRZ RjD_E',
	    'PoTJ\\J RSO\\O R^OlO R^UlU R][l[ RTRUW RXJXZ R[R[W R`I`U ReEe[ R^JjIiU RXC]H RVFRJ RWCUH R]YR[ RjD`E',
	    'PoUJ\\J RSO]O R_UkU R_ZkZ RXJXY R[Q[V R_P_[ RePeZ R_PkPl[ RXC]H RjJkN RhIkL RjDhI RWERJ R^HaN RcHfN RaC_I RfCcI RTQUV R]XRY',
	    'PoTJ\\J RSO\\O R^PlP R_UjU R`[d[ RXJXY RdLd[ R_LjLjV RXC]H RhFkI RWERJ RdGdJ RWCUG R`E]K RTQUV R[Q[V R]XRZ RiD_E',
	    'Po^GlG RTJ[J R`MiM RRO[O R]TlT RWJWY RdJdQ RhPh[ RWD\\H RgHlM RWERJ RbH]M R[RZW RdCaK RTRTW R[XRZ RaR`Y',
	    'Po`IhI RTJZJ RRO[O Ra[g[ RWJWY RbTb[ RgYg[ RbReU RWC[H RdCkJ RdC\\K RjVlZ R^MiMfS RVCTI RTRTW RZQYV R^U\\ZSZ',
	    'PoTJ[J R`KjK RSO\\O R^PlP Ra[d[ RXJXY R^G^J RePe[ ReCeG R^GkGkJ RXC\\H RVFRJ RiTlY RWCUH R`T]Y R[QZV RTQUV R\\XRY',
	    'Po]FlF RTJ\\J R`NjN RSO\\O R]UlU Ra[d[ RXJXY R`I`N ReCeF ReSe[ R`IjIjN RXC]H RWERJ R_QjQeS R[Q[V R]XRZ RTRUV',
	    'PoTJ\\J RSO\\O RXJXY RfVj[ RXC\\H RhKkN RdJ`N RjEiI RdV_Z RkQiU RVFRJ RTRUV ReSgY RdIhK ReSbX RWCUH R`E_I R`R_V R[Q[V ReCcK R]XRZ ReOfV',
	    'PoTJ\\J R_LkL RSO\\O RdRjR Re[l[ RXJXY R^G^J RdCdG ReLeZ R^GkGkJ RXC\\H RXCRJ R[Q[V R`P^Y R]XRZ RTQUV R_Ud[l[',
	    'Po^FlF RTJ\\J RSO\\O R_VeV Re[i[ RXJXY R_Q_X R_QfQfV R^MkMj[ RXC\\H RVFRJ RdHkK RWCUH RcJ^L R[Q[V ReCcI R]XRZ RTRTW',
	    'PoeGlG RbGiG R`GgG R^GcG RTJ\\J RSO]O RbSjS R`SgS R^TdT RXJXY R[Q[V RlLkN RXC]H RlYk[ RWERJ RbEdJgMjN ReOiP RiTeWgZj[ RfDiD RWCUG RhJ^N RbOdX RhVbY]Z R]XRZ RTQUV',
	    'PoTJ[J RSO[O RTQUV RXJXY RZQZV RgWl[ R`UcX RXC\\H RgHkM RjIeN RaV\\[ RjUdZ RVFRJ RaJ]N ReQiY R^HcJ R^TaV ReEkEhK RdQkQhX RWCUH R]EcE`L R]QcQ_X R\\YRZ',
	    'Po]GlG RTJ[J R`LiL RSO\\O R`QiQ R\\UlU RWJWY RZQZV R`C`U RiCiU RhWl[ RWC\\H RaW^Z RUFRJ RVCTH RTQTV R\\XRY',
	    'Po]GlG RTJ[J R]LkL RSO\\O R_SjS R_WjW Rf[j[ RXJXY R_O_[ R_OjOj[ RXC\\H RgIlM RVFRJ RWCUH RbG`L RTRUV RdCdG R[Q[V R]XRZ',
	    'Po_JkJ RTJ\\J R_NkN RSO]O RiYlY RXJXY R_Q_Y R_F_N ReCdF ReNe[ R_FkFkN R_QlQlY RWC]H RWCRJ R[Q[V R]XRZ RTRTW',
	    'PoTJ\\J RRO]O R`PjP R`ZkZ RXJXY R^F^J R`J`[ ReCeF R`JjJjP R`SkSk[ R^FlFlJ RXC]H RWFRJ RWCUH R\\Q[V RTRUV R]XRY',
	    'Po^GlG RTJ\\J RSO\\O R_TkT R_ZkZ RXJXY R_N_[ RbCbK ReNeZ RhChK R_NkNk[ RXC\\H RVFRJ RWCUH R[Q[V RTRUV R]XRZ',
	    'Po`IkI RTJ\\J R`MkM RSO]O R_UlU R_ZlZ RXJXY R_Q_[ R`D`M R`DkDkM R_QlQl[ RXC]H RWFRJ RWCUH R\\Q[V RTRUV R]XRY',
	    'PoTJZJ R^NjN RRO[O R\\UlU RWJWY RZQZV R^H^T RbCbT RfCfT R^HjHkT RgVl[ RWC[H RaV\\[ RVCTI RTQTV R\\XSZ',
	    'Po_IkI RUJ\\J R_MkM RSO]O Rg[j[ RXJXY R[Q[V R_D_M R_DkDkM RXC]H RaP]T RWERJ RhQdZ RTQUV RdQbU]XRY RbM`RlQk[',
	    'Po^FlF RTJ\\J R^NkN RSO\\O R^RkR RXJXY R[Q[V R^J^R RdCd[ R^JkJkR RfTlZ RXC]H RXCVGRJ RTQUV RcS]YRZ',
	    'PobIiI RTJ\\J RaMjM RSO\\O RbViV Rg[l[ RXJXY RbQbX RfFfM RbQiQiV R_DlDl[ RYE\\H RXDRJ R[Q[V RTRUW R\\XRZ R_D_S^Z',
	    'PoUJ\\J RaKjK RSO\\O RbViV R_ZlZ RXJXY R_D_[ RbPbV ReGeO RbPiPiV R_DlDl[ RXC]H RXCRJ R[Q[V R]XSZ RTQUV',
	    'Po_GlG RTJ\\J R]MlM RSO]O R`UjU R`ZjZ RXJXY R`Q`[ RbCbM RhChM R`QjQj[ RXC]H RXCRJ R\\Q[V RTRUV R]XRY',
	    'Po^HkH RTJ[J RRO\\O RXJXY RbVc[ RdCdT RXD\\I RgMkR RaN]R RWERJ RjUlZ RdGhO R[RZW RdG`P R_V][ RTRTW RfVf[ R\\XRZ',
	    'PoXHeH RULiL RSPkP RSS^S R`SkS RaWjW RTW]W R`[l[ RYSY[ R_H_P RfSf[ RdMcP RYM[P R_DdGlI R`CYGRI R^ZYZT[',
	    'Po_IiI RTJ[J R]MlM RSO\\O Ra[d[ RXJXY RdMd[ R_DiDiM R_PaS RXC\\H RkOhR RXCRJ R[Q[V RTQUV RbV`W\\XRZ ReOgTlY',
	    'Po_EjE RTJ[J R]KlK RSO\\O RaQdQ R\\[l[ RTRUW RXJXZ R^S^[ RbSb[ ReHeP RfSf[ R^SjSj[ RXC\\H RVFRJ RWCUH RhFeH R[RZV R\\YRZ',
	    'Po^GjG RTJ[J R]KlK RSO\\O R^UkU R\\[l[ RTRUW RWJWZ R[RZW RdCdK RdQd[ RgKgQkPkQ RWC\\H RUGRJ RWCTI RbL`Q]Q R\\YRZ',
	    'Po]GlG RTJ[J RSO\\O Rf[l[ RXJXY RaNa[ RaCaI RfKf[ RhChI RlWl[ RXC\\H R`O\\S RkNfS RVFRJ RWCUH RaK^R RTRUV R[QZV R\\YRZ',
	    'Po]FkF RTJ[J R^JjJ R]NkN RSO[O RWJWY RZQZV RaRaZ RgWl[ RXC\\H RjSfT RVFRJ ReQhY RWCUH ReCdO_S\\T RTQUV RdMgV R\\XRZ RfY]Z RdJeT',
	    'Po\\GeG RTJZJ R\\NdN RROZO R\\RdR R[VeV Rg[l[ RWJWY RYQYV R\\J\\R R`C`[ R\\JdJdR RhChZ RWCZG RTQTV RVCSJ RlVl[ R[XRY',
	    'Po]GkG RTJ[J RRO[O RWJWY RdCd[ R_MbO RWD[H ReRlY RdOfT RhMlP RWERJ RTRTV RiJfP RZRYV R`J]Q RdObS\\XRY',
	    'PoeFkF RTJ[J R]JlJ RSO\\O R_RjR R_VjV Rf[j[ RXJXY R_N_[ R`E`J ReCeJ R_NjNj[ RXD\\I RWDRJ RTRUW R[RZV R\\XRY',
	    'Po^GlG RUJ\\J R^NlN RSO]O Rd[i[ RXJXY R[Q[V RbCbJ ReJeN RiCiJ RXC]H RaW\\[ RWERJ RTQUV RcNaX R]XRY RbRjRi[',
	    'Po^FdF RTJ[J R]JlJ RSO\\O RbQeQ RTQUV RXJXY RZQZV R^O^Y RbLbY RlWl[ RXC\\H RiDlG RVFRJ RWCUH R\\YRZ RgCgPhZ RfX\\Y',
	    'Po]GlG RTK[K RaMhM RRO\\O R]RkR Ra[d[ RXKXY ReRe[ RXD\\I ReGkM RWERJ RdG^M RiUkY R[RZW RdDaJ R`U^Z RTRTW R\\YRZ',
	    'Po^ElE RTJ[J RSO]O RXJXX RXD\\H RdJgKkO RdJ_O RjGiJ RXDRJ RTRUV RbP_TcWl[ R`G_K RaRiSeX][ R[Q\\WRY ReEdK',
	    'PoRE_E RbIlI RSJ_J RZSdS RVVhV RS[k[ RZHZP R_S_[ RbEbM RhIhP RYX[[ ReXc[ RWESI R_OcRlT R_OYRRT RjDaE R[MTN R_MWN',
	    'PodFkF RTJZJ RcMiM RRO[O RdPkP Rh[l[ RWJWY ReJeP RdCdI RcShSh[ R^IlIkL RlWl[ RWD[H RWERJ RTRTW RZRYV R[XRY RcSbZ R^I^T]Z RfMaM',
	    'Po`HjH RTJ[J R`LjL RRO\\O R^OlO R^SlS Rd[h[ RXJXY R`D`L RhOh[ R`DjDjL RaUdX RXD\\H RWDRJ RTRTW RZRZV R\\XRZ',
	    'PoROZO RTRTW RTJXJWY RYRYW R]KaK`[ RWD[H RVFRJ RfHkHjN RhQc[ RVCTH RgOjY RgDdN RZYRZ RhLiW RbE]F]S[[ RgMfW',
	    'PoTK[K R`LiL RRO\\O R^RjR R^[k[ RWKWZ RdLd[ RWD\\I RdDlL RWERJ RcG^L R`TaY RiTgY RZRZW RdCbH RTRTW R\\YRZ',
	    'PoTJ[J RRO\\O Ra[h[ RWJWY RbTb[ RhXh[ RbSeV RWD\\I RgKaQ RWERJ RcI^N RiUkY RZRZW RdGbL R^U]Z RhGeM RTRTW RjQfQ R[XRZ RaC`HkGjQ',
	    'Po^GlG RTJ\\J RSO\\O R_QjQ Ra[e[ RXJXY R_K_Q ReCeG ReQe[ R_KjKjQ RjVlZ RXC\\G RWERJ R`T^Y RTQUV R[Q[V R]XRZ',
	    'PobIkI RTJZJ RSO[O RaOlO RTRUW RWJWY R]K_N RWD[H R]EaF RWERJ RiTl[ R_S\\[ RZRYW RfCfPbZ RfMdW RZXSZ RiYaZ RkYdY',
	    'Po^HdH RfHlH RTJZJ R^LdL RfLlL RSO[O Ri[l[ RTRUW RWJWY R^D^[ R^DdDdL RfDfL RfDlDl[ RWD[I RWERJ RZRYV R[XRY',
	    'Po_GdG RTJ\\J RfNlN RSO]O R_VjV R_ZjZ RXJXY R\\Q[V R_C_N R_Q_[ RgCgN R_QjQj[ RlKlN RXC]H RWERJ RWCUH RkFfH RdOdR RTQUV R]XSZ RdMYO',
	    'Po]FlF RTJ[J RSO\\O R\\RlR RXJXY R[QZV R_L_R RaCaI RgCgI R_LjLjR RfVj[ RXC\\H RVFRJ RdIdSgY RWCUH RdRbW`Y\\[ RTQUV R\\XSZ RdNcV',
	    'Po_HjH RTJ[J R_LjL RSO\\O R]OlO RdTkT Rd[l[ RXJXY R_D_L ReOeZ R_DjDjL R_U`XcZ RXC\\H RVFRJ RWCUH R[QZV R_R]Z RTQUV R\\XRY',
	    'Po^FlF RTJ\\J RSO\\O R^RkR RXJXY R[Q[V R^J^R RdCd[ R^JkJkR RfTlZ RXC\\H RWCVGRJ RaLaP RhLgP RTQUV RcT]YRZ',
	    'PoeIjI RUJ\\J RSO\\O RbWhW Ri[l[ RXJXY R^N^[ RbRbX ReIeN RbRhRhW R`EjEjN R`E_NlNl[ RXC\\H RXCRJ R[QZV RTQUV R\\XRY',
	    'Po]GjG RZJlJ RePkP RZSdS RVViV RS[k[ R_S_[ RcCcJ ReJeO RkMkP RYX[[ ReXc[ RXLSP RUDYE R]D\\H R_J^NYP RSHWI R_PaQlT R_OZRRT',
	    'Po^FkF RTJ[J R^KlK RSO\\O RbOiO RXJXY RbHbO ReCeF RiHiO RXC\\H RVFRJ RaTfYl[ RWCUH R_SjSeX_[ R[QZV RTQUV R]XSZ R^F^S\\[',
	    'Po^IlI RTJZJ RRO[O RaRkR RaVkV RaYkY RWJWY RaNa[ RfEfN RaNkNk[ RWD[H RWCVFRJ RZQYV RTRTV R[XRY R^F^R\\Z RdE^E RjDaE',
	    'Po^JlJ RTJ[J RRO\\O R`QjQ R^TlT R_YkY RXJXY R`L`Q R`U_X RfQf[ R`LjLjQ RXD\\I RXDRJ RTRUW R[RZV R_FiFiJ R\\XRY RcDbJ',
	    'PoTJZJ R^LjL RRO[O RWJWY RZQYV R^D^L RjDjL RfLfQkOlQ RWC[H RbQ]V RgDaJ R`TdXk[ RaFgH RVCTI RbL`P]Q RaSiTeXbZ\\[ RTQTV R[XRY',
	    'Po`GlG RTJ[J R`MjM RSO\\O R`PjP RXJXY R`I`P R`IjIjP RXC\\H RcQ]V RVFRJ RaTdXl[ RWCUH RaC^I RaSiTeX_[ RTQUV R[QZV R\\XRY',
	    'PoTJ[J RSO\\O R]SlS RTQUV RXJXY RZQZV RbPePe[ RdSlZ RXC\\H RbL^N ReI`N R]IkIiM RdS]Z RVFRJ RbFhI RWCUH ReIeNdR R_EjEdG R\\YRZ',
	    'Po_IiI RTJ[J RRO\\O R`PiP R`TiT R`XfX RXJXY R_D_I R`L`X R^LjLi[ R_DjDjI RXD\\H RWDRJ RTRTW R[RZV R\\XRZ RdX]X RlWhWbX',
	    'PoTJ[J R^JcJ RfJkJ R_MjM RSO\\O R]QlQ Rc[h[ RXJXY R[QZV R^E^J R^EcEcJ RfEfJ RfEkEkJ RXC\\H RVFRJ RWCUH RbQ`UjTh[ R\\XRY RTQUV',
	    'Po^IkI RTJ[J RSO\\O R^PjP R^SjS R]WlW RWJWY RZQZV R]D]H R^M^S RdGd[ R^MjMjS R]DkDlH RWC\\H RVFRJ RWCTH RTQTV R\\XRY',
	    'Po^FlF RaJiJ RTJ[J RaNiN RSO\\O R]RlR RgWkW R_ZlZ RXJXY R_R_[ RaCaR RgRgV RiCiR RXC\\H RWCVGRJ R[QZV RcRbW R\\XRY RTQUV',
	    'Po^FlF RUJ[J RSO\\O RgXkX RXJXY R^K^O R_Q_X ReCeF ReMe[ R_QkQkX R^KlKlO RXC]H RVFRJ RTRUV RaGbK RiGgK RWCUH R[QZV R\\XSZ',
	    'Po]JcJ RTJ[J RSO[O R]OcO R]TcT Ri[l[ RTRTV RWJWY R[QZV R]D]T R]DcDdT RgFgU RlCl[ RWC[H RbVdZ RVCTI R^V\\[ R[XRY',
	    'Po[FkF RZJkJ RZSdS RVViV RS[k[ R_S_[ R`C`J RgCgJ RYX[[ ReXc[ RXKTP RTDYE ReLkO RSHWJ R_LZO R_PcRlT R_OZRRT',
	    'Po`HjH RTJ[J R`LjL RSO\\O R^OlO Rg[j[ RXJXY R[QZV R`D`L R`DjDjL RXC\\H RaQ]U RcT^Z RfWb[ RVFRJ RhRdZ RWCUH R\\XRY RTQUV R`SkSj[',
	    'PoTJ[J R]JbJ RiLlL RSO[O R]PbP RWJWY RZQZV R]E][ RdDdK RdDiDjL RlJlL RWC\\H RWCRJ RcPkPhVeYa[ RbD]F RTQUV RbU\\V R[XRZ RdQgWk[',
	    'PoTJZJ R]KcK ReLkL RRO[O R]PbP R]UcU RTQTV RWJWY RZQYV R]D][ R]DcDcK ReDkDkK RgVl[ RWC[G RUGRJ ReQhY RVCTI RdPkPhVeYa[ R[XRY',
	    'Po_IkI RUJ[J R_MkM RSO\\O RcViV R`WgW RXJXY R^P^[ R_D_M ReDeV R_DkDkM RXC\\H RVFRJ RhSiX RWCUH RTQUV R[QZV R^PlPl[h[ R\\XSZ',
	    'PodFlF RTJ[J R_NjN RSO\\O R_RjR R_VjV RWJWY RZQZV R_J_V RdCdJ R_JjJjV RgXjY RWC\\H RVFRJ RVCTH RbX\\[ RTQTV R\\XRY',
	    'Po_IjI RTK[K R_MjM RRP\\P R_RkR R_VkV R^[l[ RXKXZ R_E_M ReNe[ R_EjEjM RXD\\I RWERJ R`O^T RTRTW R[RZW R\\YRZ',
	    'PoUJ\\J R_KjK R_OjO RSO]O R^RkR R^VkV R\\[l[ RTRUW RXJXZ R_F_O ReRe[ R_FjFkO RXC]H RWFRJ RWCUH RdCcF R[R[W R]YR[',
	    'Po^IjI RTJ[J R^NjN RRO[O R]QlQ RWJWY R^E^M R_Q_[ RdEdM R^EjEjM RfVk[ RWD[H RjUfV RWERJ RTRTW RdQgX RZRYW ReY^Z R[XRZ',
	    'Po^ElE RTJ[J R_MjM RRO\\O R^UkU R^ZkZ RXJXY R^P^[ R_H_M RePeZ R_HjHjM R^PkPl[ RXD\\I RWDRJ RTRTW R[RZV R\\XRZ',
	    'Po^FlF RTJ[J RRO\\O RfQjQ R_ZkZ RXJXY R_S_[ RbCbH RhChH R_SkSk[ RXD\\I RbM^Q RXDRJ R[RZW RcJaP RTRTW R^JkJjQ R\\XRZ',
	    'Po]JlJ RTJ[J RSO\\O R^TcT RgTkT R^ZkZ RXJXY R[Q[V R^O^[ ReEeZ RfOkOk[ RXC\\H RVFRJ RWCUH RbN^O R\\XRY RTRUV RjC^E',
	    'Po]FcF R]IcI RTJ[J RSO\\O RgOjO R]TlT RXJXY R`C`P RdDkDkN RXC\\G RVFRJ RdSeVhYl[ RWCUH RcV`Z\\[ RTQUV R[QZV RdQbX RgDeN RcM\\M R\\YRZ',
	    'PodHlH R\\HbH RTJZJ RROZO RWJWY RhJhW RkMkU ReLe[lYl[ RVEZH RUFRJ RTRTW R^C_F RVCTH RkMbQ R`[][ RYRXV ReCcK RZXRY R^H^Q\\Y R^MaMaV`[',
	    'PoTJ[J R_JfJ R]MiM RRO[O R_OgO R_RgR R_XgX Rg[j[ RWJWY RZQZV R_T_Y R_TgTgX RWC\\H RUGRJ RVCTI RaC]K RTQTV R\\XRY R_HlHlUk[',
	    'PoTJ[J R`NiN RRO[O R^SlS RWJWY RdSl[ RWD[H RhJlN RlIhJ RcV^[ RWERJ R^HaJ]N RTRTW RjEfFjM RdRbX RZRYV R_EdE`M R[XRY ReNdV',
	    'Po]FlF RTJ[J R]NlN RSO\\O R`ZjZ RWJWY RZQZV RaQa[ RaCaI RhChI R_SjSk[ RWC\\H R`R\\V RUGRJ RVCTI RcK_T RTQTV R\\XSZ',
	    'PocGlG RWQZQ RZTdT RVWhW RR[l[ RZJZP R_T_[ RZX[Z RdXc[ RVMRO RYJTO RRJ_J]M RcGdJhNlP RVG]I RdCaJ RjHeMaO RTE^EYG R_OeRlT R_OWSRU',
	    'PoSH]H RZTdT RWWhW RS[l[ RYEYP R_T_[ RXH\\L RZX[Z RfKjO RdJ`N RjGhI ReXcZ RYHRO RaF_J R_PdSlT R_PXSRU RdEdL R\\DSE',
	    'PoTJZJ R\\LbL RSO[O RTQUV RWJWY RZQYV R`E`[ R_NcR RWC[G RUGRJ RhTkZ RfScZ RVCTI RhCgQiY RkIjP R_J\\V RcD\\F RdJcO RgMhW R[XRY RgMfV',
	    'PoaJlJ RTJZJ RbOkO RSO[O RaTlT RWJWY RZQYV R_F_[ RbEiEiJ RhWk[ RWC[G ReWa[ RUGRJ RfNfTiZ RfTdY RVCTH R_E\\N RcKaQ RTQTV R[XRZ RfPeW',
	    'PoTJ[J RaJhJ RSO[O R`[c[ RTQUV RXJXY RZQZV R^N^[ R^NcNc[ R_UaV RXC\\H R_DdDkK RVFRJ RdD]K ReSg[ RhTl[ R_QaQ RgMeT RkMhT RWCUH R\\XRZ',
	    'Po_GkG RTJZJ RRO[O RfQiQ Ra[h[ RWJWY RZQZV RbTb[ RhXh[ RbRfT RjVlZ RWC[H ReL`Q R`E\\I RbI]N R_JgM RVCTI RcGaL RgGdN RTRTW R^U\\YRZ RkHjQ',
	    'PoTJ[J R_KjK RSO\\O R]OlO RaSjS RXJXY RdFeI RXC\\H RiFkI RVFRJ RaSfXl[ RWCUH R_E]J RaR]Z RiTeX_[ RTQUV R[QZV RbK`V RiD_E R\\XRY',
	    'Po[HlH RTJ[J RdLiL RSO[O R_OkO R]SlS RTQUV RXJXY RZQZV R_D_O RdCdK RdOd[ RiDiK RdSkY RXC\\H RcT]Z RVFRJ RWCUH R\\XRZ',
	    'Po^HkH RTJ[J R^KlK RRO\\O R^VjV R^ZjZ RTRTW RXJXY R^D^P R^R^[ R^RjRj[ R^DkDkH RXD\\H RfMiP RdIgO RWDRJ R[RZW RdN]P R\\XRZ RlNlQjP',
	    'PoSD^D RaHlH RSL]L RaNkN RZTdT RVVhV RR[l[ RYDYH RYLYO RSH^H^K R_T_[ RZXZZ RdXcZ RcD`K RSDSP^OdRlT R_PXSRU',
	    'Po^JkJ RTJZJ R\\MlM RSO[O Ri[k[ RWJWY RZQZV R^Q^[ R^E^J RbQb[ RdMcQ RdCdJ RfQf[ RkEkJ R^QkQk[ RWC[H RVCTI RTQTV R\\XSZ',
	    'PoaJlJ RTJZJ RbMjM RSO[O RbQkQ RaUlU RWJWY RZQYV RfCfY RbFjFjM RWC[G RWCRJ RTQTV R\\E`E]N`N][ R^Vc[l[ RSZ[Y',
	    'Po^JjJ RTJ[J RSO\\O R^OjO Ra[h[ RWJWY RZQZV R^D^O RbSb[ RdDdO RhXh[ R^DjDjO RWC\\H RUGRJ RiTlY RbQfR RVCTI RTRTW R^T]YRZ',
	    'Po]GkG RTKZK R_MiM RRP[P R]RkR R^VjV R\\[l[ RWKWZ RdJd[ RWE[H RfGkM RWCVGRJ RcG]M RdCaJ RTSTW RZRYW R[YRZ',
	    'Po]IdI RTJZJ RfKlK RRO[O RfRlR R]WcW Ri[l[ RTRTV RWJWY R]P]Y R`C`O R]PcPdW RfElEl[ RWDZH RUFRJ RVCTH RYQYV RZXRY RfEfSeY',
	    'PoTK[K RaKjK RRP\\P R`SjS R`XjX R^[l[ RXKXZ R^G^J R`N`W R`NjNjW R^GlGlJ RXD\\I RWERJ RdCdF RTSTX R[RZW R\\YRZ',
	    'Po^GhG RTJ[J R]MlM RSO\\O R`VjV R`ZjZ RWJWY RZQZV RaQa[ RcCcL R_RjQj[ RWC\\H RfNhP RjEiH`Q RVFRJ RVCTH RTQTV R\\XRY',
	    'Po_HkH RTJ[J R_LkL RRO\\O R_XiX Rg[k[ RXJXY R_E_L R_S_X R_EkEkL RdShU RXD\\I RbN]R ReSbV RWDRJ R[RZV RTRUV R\\XRZ R`PlPlUk[',
	    'Po^HkH RTJ[J R`KeK RSO[O R`UeU RWJWY RZQZV R`O`W R`OeOeU RXC\\H RiCkF RhUb[ RWCUGRJ RiZk[ RjNfW RlXk[ RTQUV R]XSZ RgCgMgQiZ R^H^S\\Z',
	    'PoTJ[J R^MlM RSO\\O R`UlU Rj[l[ RWJWY RZQZV R`P`[ RdPd[ RhPh[ R^IlIlM R`PlPl[ RWC\\H RUFRJ RVCTI RTQUV R\\XSZ R^F^S]Z RkD^F',
	    'Po]IlI RTJ\\J RSO]O R^OkO R^SkS R^WkW R][l[ RTRUW RXJXZ R^L^S R^EeEe[ R^LkLkS RXC\\H RWFRJ RWCUH R[R[W R]YR[ RjDbE',
	    'Po\\HlH RTJ[J RSO\\O R_UkU R_ZkZ RTQUV RXJXY RZQZV R_P_[ RePeZ R_PkPk[ RXC]H RVFRJ R_HaN RdHfN RhHkM RWCUH RaC_H RfCdI RkChI R\\YRZ',
	    'PocGlG RTJYJ RSOZO RVJVY RYQXV R]H][ R`H`W RVCZG RcGhLlN RVCSJ RdCbI RiHeMaN RgPbR RhSbV RjWa[ RTQTV R^C\\L RZYRY',
	    'PoTJZJ R]JcJ RSO[O R]PcP RWJWY RZQYV R]D]V R[DdDc[ RfDf[ RWC[G RTRTV RiLlS RVCSI RfElEiM RkVhV RcU[V R[XRY',
	    'Po^GkG RTJ[J R_KkK RSO\\O R^OlO R^TlT RXJXY R[Q[V RfVj[ RXC\\H RaCbF RWCVFRJ ReGdSfX RhCgF RcW`Z][ RTQUV R\\XRZ RdNcW',
	    'PoTJZJ RROZO RWJWY RYRXV RdKd[ R\\L_N RWD[H R\\D_G RkNhQ RUFRJ RTRTV RVCTH RhYdZ R^S\\Z RkJdK RZXRZ RgKjX RjDaEaR_[',
	    'Po]GlG RTJZJ RRO[O R_PjP RWJWY RYRYV R_J_P RaSa[ RcCeG R_JjJjP RgVl[ RWD[H RcP^U RWDRI RdPhY RlSfU RTRUW R[XRY RfZ_Z',
	    'Po^IjI RTJZJ R^NjN RRO[O RTRTW RWJWY R^E^N RdEdN RgRg[ R^EjEjN RWD[H RbN]S R`W][ RVFRJ ReNiRlS RWCUH RZRYW R[XRZ RaR`X',
	    'Po]HlH RTJ[J RRO\\O R^PdP R^TdT Ra[c[ Rg[k[ RXJXY R^L^[ R^LdLdZ RgLgV RkKk[ RXD[H RWDRJ RTRTW R`CaF R[RZV RiCgH R\\XRZ',
	    'Po_JlJ RTJ[J RRO\\O RbRlR RbVlV RbZlZ RWJWY RbMb[ ReDeJ RbMlMl[ R_DlDlJ RXD\\H RWERJ RTRTW R[RZW R\\XRZ R_D_S^Y',
	    'Po^GlG RTJ[J R^KkK RSO\\O R]PlP RaSkS R_[l[ RXJXY RdGdO RfSfZ RXC\\H RVFRJ RTRUV R`DaG RWCUH R`S\\[ RiChG R[QZV R`P^Y R\\XSZ',
	    'Po_HkH RTJ[J R_LkL RRO[O R`[d[ Rh[l[ RTRUW RWJWY R_E_L R]OdOd[ R_EkEkL RfOlOl[ R^Q`T RWD\\I RfQiT RjVfW RWERJ RdU`V]X RlThV RZRZV R[XRZ',
	    'Po^FlF RTJ[J RSO\\O R]PlP Re[i[ RXJXY R^K^N ReCdF ReMeP R^KlKkN RXC\\H RaW][ RVFRJ RaGaK RWCUH RhGgJ RTQUV R[QZV RcPaX R\\XRY RbTjTi[',
	    'PoUJ\\J R_NjN RSO]O R^QlQ R`ZjZ RXJXY R[Q[V R^G^J R`T`[ ReCeG ReHeT R`TjTj[ R^FlGlJ RXC]H RWERJ RWCUG RTQUV R]XRZ RiJaK',
	    'Po^GlG RTJ[J R]OlO RSO\\O R^SkS RXJXY R[QZV RbFb[ RfGf[ R^KkKkS RWC\\H RWCRJ R`CbH RhCfI RTQUV RbR_W\\YRY RgRhVlY',
	    'Po]FlF RTJZJ RSO[O R]TkT RWJWY RZQZV R]J]M RdCdJ ReTe[ R]JlJlM R_OcR RiWlZ RWC[H RhQlU RdL`O RgNaT RVCTI RTQUV R`W\\YSZ',
	    'Po_GkG R`JjJ RTJ[J RRO[O R]TgT RWJWY RbOb[ RlXl[ RaTdW RWD[H RbT]Z RfOdR RWERJ RTRTW R_O_S R`C]J RZRZV R]MiMiVj[ R[XRZ',
	    'PoZElE RZTdT RVWhW RR[l[ R_T_[ RbCbF RbKbO RhKhPkOlP RYX[Z ReXcZ RaE]I R\\NZP RTDXE RZJgHkJ RVLUO RRHVI R_PaQlT R\\K\\O R_PYSRU',
	    'Po]FcF RTJ[J R_OkO RRO\\O R]SlS R^ZkZ RXJXY R^U^Z RdNdZ RdGdJ RkUk[ RXD[H R_L^P RWDRJ R^G_K RjFhK R[RZW RTRUW R\\XRZ RjDaE',
	    'Po_FjF R^JlJ RTK[K RRP\\P R^PkP R][l[ RXKXZ R_S_[ RcSc[ RgSg[ R_SkSk[ RXD\\I RhLkP ReCeJbM_P RWERJ R[RZW R\\YRZ RTRTW',
	    'Po^ElE RaHjH RTJ[J R^KlK RRO[O R_TlT Rd[h[ RWJWY RaKaQ RhQh[ RaVdX RWC[H ReLiPlQ RVCTI RkLgN RTQTV RZQYV RfP_Q R\\XRZ R^E^S\\Z',
	    'PoTJYJ RaLiL R^MgM RROZO R_[b[ Rh[k[ RTRTW RWJWY RYRYW R\\PbPb[ RdPkPk[ R]R`T RVEZI RfRhT RfIjM RfDjH RUFRJ RbD\\I RcG^M RVCTI RbU\\X RkUdX RZYRZ',
	    'PoTJ\\J RSO\\O R`ZjZ RXJXY R[Q[V R^F^J R`T`[ ReCeF R`TjTj[ R^FlFlJ RXC]H ReLkS RhIkM ReM^S RWCVFRJ RbI^M R]XRZ RTRUV',
	    'Po]FlF RTJ[J R]MlM RRO[O R_QjQ R`ZjZ RWJWY RZQYV R`T`[ RdCdF ReGeS R`IjIjQ R`TjTj[ RWC[H RVCTI RTQTV R\\XSZ R]F]R\\[',
	    'PoTJ\\J R_NjN RSO\\O R_RjR R_VjV RXJXY R_J_V ReCeJ R_JjJjV RgXkZ RXC]H RXCRJ R_D`H RjDhH RbX\\[ RTQUV R[Q[V R]XRZ',
	    'PoaIhI RTJ\\J RSO\\O R_OjO R_RjR R`ZjZ RXJXY R_L_U`[ R_LjLjR R`UjUj[ RXC]H RdClJ RWERJ RdC]J RWCUG R\\Q[V R]XRZ RTRUV R_O^X',
	    'Po]ElE RTJZJ R_LjL RRO[O RfSkS RaUiU Rh[k[ RWJWY RZQZV R^O^[ R_H_L RdUdZ R_HjHjL R^OkOl[ RWC[H RUGRJ RVCTI RcOaR_S RTQTV RfOgS R\\XSZ',
	    'Po\\HeH RTJZJ RgKlK RRO[O RgRlR R]RdR Ri[l[ RTRTV RWJWY R]L]R RdLdS RgDlDl[ RWDZH RWCUGRJ R]D^G RYRYV R[XRY RdCaH`Q_W RgDgReY',
	    'PoTJ[J RcLjL RSO[O RcWkW Rb[l[ RTRUW RXJXY RZQZV RcGcX RfCdGjGjL RcQkQkX R]J`L RXC\\H R^D`F R]Q`Q_X\\[ RVFRJ RWCUH R_Q\\Q R[XRZ',
	    'Po]FlF RTJ\\J R^MkM RSO]O R^PkP R]TlT Rd[h[ RXJXY R[Q[V R^I^R RdCdQ RhRh[ R^IkIkQ R`VcX RXC]H RWCVFRJ RgDjD R]XRZ RTQUV',
	    'PohIlI RRJ]J R[P]P RZTdT RVWhW RR[l[ RUEUN RUE]E]P R_T_[ RbEaH RbDhDhH RXGZI RXMZN ReYb[ RbMkQ R`KjKgNaP R_QeSlT R^QXSRT',
	    'Po^FlF RTJ[J RRO[O RaPiP R`ZjZ RXJXY R`T`[ RaCaI RhChI R`TjTj[ RXD\\H RbM]O ReI`N RWERJ RTRTV ReJhMlO RZRZV R\\XRZ',
	    'PoTJ[J R^OkO RSO\\O R_TdT Rf[l[ RXJXY R^F^O R_P_[ RfPf[ R^FkFkO RlXl[ RXC\\G RVFRJ RdCcF RaIfK RWCUH RgHdK`M RlRfU RTRUV R[QZV RdY][ R\\XSZ',
	    'Po_DiD RTJZJ RRO[O R\\TlT RWJWY RZQYV RcRc[ RWC[G R\\LcL]Q RdLkLfP ReNlP R]McP RcShYlZ RVCTI RTQTV R_FiI RhFcH]I RcS^Y[YRY',
	    'PoTK[K R^KkK R^OkO RSP\\P ReUjU R][l[ RXKXZ R^E^K R`S`[ RbEbK ReOe[ RgEgK R^EkEkK RXD\\I RXERJ R[RZW RTSUW R\\YRZ',
	    'Po]HlH RTJ[J RSO[O RXJXY RZQZV R]P`S RWC[G RePhS RVFRJ R`CaG RhJeP RkNeZ RWCUH RaJ^P RcN]Z RhCgG RTRUV R[XRZ RbVcZ\\Z RjVkZdZ',
	    'PoTJ[J R_KiK R_OlO RRO\\O Rg[j[ RXJXY RbUbY R_FiFiK RgUhV RXD\\H RXDRJ RcCbF RTRUW RdUeX R[RZV R^V]Z R\\XRZ R_F_SkSj[',
	    'PoZRdR RUUiU RS[k[ RTMSP R_R_[ RSLkLkP RZW[Z RjEhG RdWcZ RUESG R]E[G RbE_G RXH]J RdHkK ReDcI_J RWIRK R_NeQlS RYCXI R_NWRRS',
	    'Po_HkH RTJ\\J R_LkL RSO]O R]SlS Rg[l[ RXJXY R[Q[V R_D_L ReLeS RgSg[ R_DkDkL RlXl[ RXC\\H RiMhQ RaW^[ RWERJ R_NaQ RTQUV RbS`Y R\\XRY',
	    'Po_EiE R\\LkL RaPdP RZTdT RVWiW RS[k[ RVFVQ RYFYN R_T_[ RdJdP RYXZ[ ReXd[ RVFRJ R`C\\G RfNkO RWCUH RhF`I R_N[P R_QjU R_FfH\\J R_QRU RbHkJ',
	    'PoTJ[J RiMlM RSO[O R_VkV R_ZkZ RXJXY R[QZV R^E^M R_R_[ ReReZ RgEgP R_RkRk[ RgElElM RXC\\H RVFRJ RWCUH RdD^E RTQUV RcGcM]M R\\XRZ',
	    'Po_IkI RTJ\\J R]LlL RSO]O R_UjU R][l[ RXJXZ R_E_I R_P_U RdCdI R_PjPjU RkEkI RXC]H RWFRJ RTRUW RaVbZ RWCUH RhVgZ R[R[W R]YR[',
	    'Po_GkG RTJ[J R_JkJ R_NkN RSO\\O R_RlR R\\UlU RWJWY RZQZV R`F`R ReEe[ RWC\\H RVFRJ RWCTH R`C]K ReCeG RTQTV R\\XRY',
	    'PoTJZJ RjMlM RSO[OcP RTRUV RWJWY RZQZV R`L`[ ReDeK RgWl[ R_RcU RWC[G RbLbN R^FbJ RiUc[ RUGRJ R`H\\L ReQiY RdOkOgX RVCTI RbC_J ReDjDkKlM R_P\\XSZ',
	    'Po^JbJ RgJkJ RTJ[J R^NkN RSO\\O R^RjR RWJWY R_F_N ReCeR RgFkFkN RWD\\H RWERJ R_SdXl[ RZRZW RjSfX^[ RbE^F RTRUW R\\XRY',
	    'PoTJZJ RRO[O RfQkQ RWJWY RZQYV R^E^M RfNfQ R^EkEkM RWC[H RcQ]W RbIhL R`TdXj[ RVCTI RaHgHeJ`L RaP]R RTQTV RaNaP RaTiTfXbZ\\[ R[XRY',
	    'Po^FlF RUJ[J R`MiM RSO\\O RbXhX Rh[l[ RXJXY R^P^[ R`I`M RbSbY ReCeF RbShShX R`IiIiM R^PlPl[ RXC\\H RVFRJ RWCUH RTQUV R[QZV R\\XRY',
	    'Po_HkH RTJ[J RaMjM RSO\\O RaQjQ RaTjT R^VlV RXJXY R^L^V RaJaT RaJjJjT RXC\\H RVFRJ RhYl[ RWCUH R_D_H RTQUV R[QZV RiD_E RaY[ZRZ',
	    'Po^FlF RTJ\\J R_LjL RSO\\O R_PjP R_SjS R]VlV RXJXY R[Q[V R_I_S ReCeI R_IjIjS RhWl[ RXC]H RXCVFRJ RaX\\[ RTQUV R]XRZ',
	    'PoTJZJ R]KcK RROZO R]PcP RWJWY R]G]Y R]GcGcP RfEf[ RWD[H RVFRJ RTRTW RaScY R_D_F RVCTH RbW\\Y RYRYW RZXRY RkXhX RfElEiMlSlX',
	    'Po`JhJ RTJ[J RSO\\O R\\[l[ RXJXZ R_T_[ RbTb[ RfTf[ R_TjTj[ RXC\\H RVFRJ RcNgNjS RcEgEjJ RWCUH R`E^K R`N]U RTRUW R[RZW R\\YRZ',
	    'Po^FkF R]JlJ RTJZJ RRO[O R^QjQ RWJWY R^L^Q RaTa[ RdCdI R^LjLjQ RfVk[ RWD[H RWCUGRJ RdQgX RZRYV RdQaT\\V RlTfV RTRTW R[XRY ReZ_Z',
	    'PoTJ[J RRO\\O Rf[l[ RXJXY R]I]M ReCeI R`N`ShSg[ R`NiNiS R]IlIlM RlXl[ RWE[I R_E`H RjDiG RbW^[ RXDRJ RTRUV R[QZV RcT`Y R\\XRZ',
	    'Po^IjI RTJ[J R^LjL RRO[O R^PjP R\\SlS RWJWY R^F^O RdPd[ R^FjFjO RdSjY RWD\\I RWDRJ RcCcF RdS]Z R[RZW RTRTW R[XRY',
	    'Po^JjJ RTJ[J R^NjN RSO\\O R^RjR Ra[g[ RWJWY RZQZV R^F^R RbUb[ RcCcF RgXg[ R^FjFjR RbSfU RWC\\H RUGRJ RjVlZ RVCTH RTRTW R^V\\YRZ',
	    'PoTJ[J R^KjK RSO\\O R`[d[ RWJWY RZQZV R]F]I RdCdG ReSeY R]FkFkI RhTlX RWC[H RkNgR RfNiV RVCTI RdKaN]P RbR]T ReKhT RTQTV RdPeW ReSaW\\XSZ',
	    'PoTJ[J R_LeL RSO\\O R]PlP R_XeX Rf[i[ RTQTV RWJWY R_S_Y R_H_M R_HeHeL R_SeSeX RiPi[ RWC\\H RUGRJ RVCTI RZQZV R]EkEiNfN R\\XSZ',
	    'Po_DlD R_IkI RTJ[J R_MkM RRO\\O Rf[k[ RXJXY RbUbY ReDeQ RXD\\H RhTjV RXDRJ RTRUV ReTeX R[RZV R^T]Z R\\XRZ R_D_RlRk[',
	    'Po_GlG R_JkJ RTJ[J R_MkM RRO\\O R_PlP Rf[j[ RXJXY R`F`Q RfGfP RWD[H RdCfE RaW][ RXDRJ R`C]J R[QZV RTRUV R^ShSgVlVj[ RbS`Y R\\XRY',
	    'PoTJ[J RcKjK RSO[O RbPkP Rc[l[ RTRUW RXJXY RZQZV R]K`M RhTkW RXC\\H R^E`G R]Q`Q`X\\[ ReTbW RVFRJ RdEiDkJ RWCUH RcFaK RfKfPhU R\\YRZ RfMeU',
	    'Po\\EcE RTJZJ R\\KcK ReLkL RSO[O ReSkS Rc[l[ RTQUV RWJWY RZQYV R]N]W R`K`[ RbNbW ReDeZ ReDkDkZ RWC\\H RUGRJ RVCTI R\\XRY',
	    'PoTJ[J RSO\\O R_OjO R_RjR R_VjV RXJXY R[Q[V R_K_V R_KjKjV RgXkZ RXC]H R_F`J RdFeJ RhGkI RjCiG RWCVFRJ R`C_G ReCdG RbX\\[ RTQUV R\\XRZ',
	    'PodFjF RTJZJ R`LgL RdNkN RRO[O R`SkS R_VlV RWJWY RYRYV ReCeN RWD[I RWCUGRJ RTRTV R^IkHkK R[XRY RaQgQf[c[ R^H^T\\Z RjPdP',
	    'Po^GkG RTJZJ RSO[O R^SkS R_UjU R]XlX RWJWY R^D^G RdCdG RdPd[ RkDkG RWD[H RWERJ RbI_L RZRYV RaKjJgM_Q RTRUW RaKcMgOlP R[XRY',
	    'Po_IjI RTK[K R_NjN RRP\\P R\\[l[ RWKWZ R^R^[ R_E_N RbRb[ RfRf[ R_EjEjN R^RjRj[ RXD\\I RWERJ RTRTW RZRZW R\\YRZ',
	    'PoaHfH RTJ[J RRO\\O R]TkT R\\WlW Ra[d[ RWJWY R]QeQd[ R^JiJiN RWE[H RWDRJ RdCgGlI RZRZW RdC`G\\I RTRTW R[XRZ R^J^NiOaQ',
	    'PodFlF RUJ[J RSO\\O R^YkY RXJXY R^J^[ RdCdJ R^JkJk[ R`QbR ReVfW RaNiV RXD\\I RhQiS RcLeN RfP`W RXDRJ RhLeR R[RZW RTRTW R\\XSZ',
	    'PoTJ[J R^NjN RSO\\O R^QjQ R\\TlT RXJXY R[QZV R^J^Q RdJd[ R^JjJkQ RhXlZ RdTjY RXC\\H R^F`H RcFeI RhFjI RjChF RWCUGRJ R`C^F ReCcF RTQUV RdT`X\\YRY',
	    'Po^ElE RTK[K RbNiN RRP[P R`WdW RgWkW R^[l[ RWKWZ R^E^[ R`Q`W RbIbN R`QdQdW RgQgW RbIiIiN RgQkQkW RWD[I RWCVGRJ RTSTX RZRZW R[YR[',
	    'PodHlH R[HbH RTJZJ RdNkN RSO[O RcSlS RWJWY RZQYV R^NaNaY RWC[G RfWb[ RgMgTdY RVCTI RTQTV RgQjY ReCdJcP R`[][ R[YRZ R_C^H^P\\Y RgOfW',
	    'Po]ElE RTJ[J R^KjK RSO\\O R]RkR R\\UlU Rd[g[ RWJWY RZQZV RdCdR RgSg[ R^HjHjO R_VbY RWC\\H R^H^OjOlS RUGRJ RVCTI RTQTV R\\XSZ',
	    'PoTJYJ RSOZO RcSjS RcVjV Rg[j[ RVJVY RYQXV R]H][ R`H`W RcOc[ RcOjOj[ RVCZG RSQTV RcHgLlN RVCSJ RdCaJ RcGjGhJeMaN R^D\\L RZYRY',
	    'Po\\HlH RTJ[J R_KjK RSO\\O R_PjP R_SjS R_WjW RXJXY R_M_V R_D_J ReDeJ R_DjDjJ R_MjMjV RXC\\H RVFRJ RWCUH RgXlZ RbX\\[ RZQZV RTQUV R\\XRY',
	    'PodHlH R[HbH RTJZJ RSO[O RgRkR RTQTV RWJWY RZQYV RcLkLkP RdSeXh[ RWCZG RVCSI ReCcK R]MaM]M R`[][ RhLgZl[ R[YRZ R_C^H]P\\Y RaMaV`[ RdPc[',
	    'PoaFlF RTJ[J RbMkM RSO[O RbQkQ RaUlU Rb[l[ RTRTV RWJWY RZQYV RbJbQ RfCfY RbJkJkQ R\\P`P_Xb[ R]J_L RWC\\H R]D`F R_W[[ RVFRJ RWCUH R_P\\P R[XRZ',
	    'Po[GcG RTJYJ RROZO R\\QcQ RVJVY RYQXV R\\K\\Q R_C_[ R\\KcKcQ R_RcV RVCZG RSQTV RhTk[ RhIhSdZ RVCSJ ReIkIjN RhNiW RfDdO RhOfW R_P[YRY',
	    'PoTE]E RbGlG RRH_H RRJ^J RXQ\\Q RVWhW RR[k[ R_S_[ RZX[Z RdXc[ RVMRQ RbGdKgNlP R_OhT RXCXJTP RVM\\M\\P RcC`J RiIeM`O RZScRlT R_PWTRU',
	    'Po\\FlF R`JhJ RTJZJ RSO[O Rh[k[ RWJWY RZQYV R]M][ R`C`J RdJd[ RhChJ R]MkMk[ RWC[G RaQbW RhQiW RhQfW RVCSI RaQ_W RTQTV RaQ_P RhQfP R[XRZ',
	    'Po]FkF RTJ[J R^MjM RSO\\O R^QjQ R]UlU RXJXY R^J^Q RaCaH RgCgH R^JjJjQ RXC\\H RVFRJ RWCUH ReVhYl[ RcV`Y\\[ RTQUV R[QZV RdQbX R\\XRY',
	    'Po^HkH RTJZJ R^LkL RSO[O R\\UkU RWJWY RZQZV R^D^L RdTd[ R^DkDkL R_PbR RhWlZ RWC[H RhRlU RdDdL`P R`W\\Z RgNaT RVCTI RTQTV R[XSZ',
	    'Po]HkH RTJZJ RRO[O R]PkP R]SkS R\\WlW RWJWY R_L_S RdDd[ R]D]LjLiS R]DkDkL RWD[H RWDRI RTRTW RZRZW R[XRZ',
	    'Po]FcF ReFlF R]IcI ReIlI RTJ[J R]McM ReMlM RRO[O R_VjV R_ZjZ RTRTV RWJWY R`C`P RhChO R_QjQj[ RWD[H RWERJ RZRZV R[XRZ',
	    'PoTJ[J RSO[O RTQUV RXJXY RZQZV R]DcDcM ReDkDkM RXC\\G RhFeI RiIeM RVFRJ RaI]M R`F]I RfPlS RWCUH RcNaQ\\S ReR_T RgT_X RjVeY][ R\\XRZ',
	    'Po^FlF RUJ[J RaNiN RSO\\O RbWhW Rh[l[ RXJXY R[Q[V R^J^[ RbRbX ReCeF ReJeR RbRhRhW R^JlJl[ RXC\\H RVFRJ R`FbJ RWCUH RiFgJ RTQUV R\\XRY',
	    'Po^GjG RTJZJ RROZO RWJWY RVEZH ReLjKkM RgDjH RUFRJ RTRTW RbJbL RdMlQ RVCTH RgHeL RbC^H]LcM\\P RdP^S RgS^W RYRYW RZXRZ RjVcY][',
	    'PoTJZJ RSO[O R^QkQ Ra[h[ RWJWY RZQZV R^F^P RbUb[ RhXh[ R^FkFkP RWC[H RcCcF RiUlZ RdH`KhM RaReT RVCTI RcIhIdMaO RTRTV R^U\\YRY',
	    'Po_JjJ RTJ[J RRO\\O R^RkR R^VkV RXJXY R]G]J R^N^V RdJdV R^NkNkV R]FlGlJ RgXkZ RXD\\H RcCeF RWERJ RTRTW R[RZV RaX\\[ R\\XRZ',
	    'Po^FlF RTJZJ RRO[O R^PkP R`UdU Rf[l[ RTRTV RWJWY RYRYV R`Q`[ RbFbP RdCdF RgQg[ RgFgP R^KkKkP RlXl[ RWD[H RWCUGRJ RkSfU RdZ_Z R[XRY R^F^S\\Z',
	    'PoTFlF RYN`N RXWiW RU[l[ RYKYP R[F[K R_C_F R`T`[ RTHjHjK R[X[[ ReXd[ R`QhU R\\TcSlU R`QZTTV RTKjLbN RcFcPkOlP RTFTSS[ R^PWQ',
	    'Po^EdE RTJ[J R^LdL RSO[O R^VjV R\\[l[ RTRTW RXJXZ R[RZW R^HdHdL R^E^QdQd[ RXC\\H RhNlQ RiMcR RVGRJ RfIjP RfFhO RWCTI RaLaP RaEaH ReEkEiN R\\YR[',
	    'PoTJZJ RSO[O RcQlQ R\\QaQ Rf[i[ RTQUV RWJWY RZQZV RaCa[ RiMi[ RWC[G RcIgL RjIdO RUGRJ RfEbJ RdSfW ReGiI ReFkFhK RVCTI R]E]LaL R[XSZ R]S\\[',
	    'Po^IkI RTJZJ R^OlO RSO[O R_RkR R_VkV R_ZlZ RWJWY RZQZV R^D^I R_M_[ ReCeI ReLeZ RkDkI RWC[H RVCTI R`J]Q RTQTV R[XSZ',
	    'Po]GlG RTJYJ RROZO R^PeP R_[b[ RTRTV RVJVY RYRXV RbIbZ RVEZH RiDkF RhWdZ RUFRJ RdSeV RjPfY RVCTH R]G^T^X RkLhW RlWk[ RZXRZ ReLaL RgCgMgQhZ R]P[Z',
	    'PoTJZJ RRO[O R`PjP RWJWY R^H^K R_S_Y R`K`P ReCeH RePe[ R`KjKjP R^HlHlK RWD[H R_DaF RjDiF RWDRI RZRYV RTRTW R_SkSkYhY R[XRY',
	    'PoTJ[J RSO\\O R`RiR R^VkV R][l[ RTRUW RXJXZ R^H^L R`L`R RdCdH RdRd[ R`LiLiR R^HlHlL RXC\\H R_D`G RjDhG RVFRJ RWCUH R[R[W R]YS[',
	    'Po_GjG RTJ[J R_JjJ RSO\\O R^QkQ RXJXY R^M^Q R_D_J RfMgQ R_DjDjJ R^MkMkQ RXC\\H RVFRJ R`TcXj[ RWCUH RTQUV RbMbQ R[QZV R^TjTgWcY\\[ R\\XRZ',
	    'Po^FlF RTJ[J R_LlL RaNjN RSO[O R`TkT R`WkW RXJXY R[QZV R`Q`[ ReCe[ RaIjIjN RXC\\H RVFRJ RWCUH RTQUV R`QkQk[h[ R\\XRZ R^F^R\\Z',
	    'Po^FlF RTJ[J R^LlL RSO[O RaRkR RaVjV R_[l[ RTRUW RXJXY R[QZV ReCeF RfNf[ RXC\\H RVFRJ RaGiK RWCUH RbN`T RhG`K R]XRZ R^L^T\\Z',
	    'PoTJZJ RROZO RaPkP RbSjS RaVkV Rb[l[ RWJWY RfNfY RWD[H R\\N^N^X[[ RVFRJ ReDaH RTRTW R\\E]I RcGgKkM R^Xd[ RVCTH RcFjFfK`M RYRYW RZXRZ R^N[N',
	    'Po^FkF RTJ\\J R]JlJ RSO\\O R_QjQ R_TjT Rg[l[ RXJXY R[Q[V R_M_T RdCeF RgTg[ R_MjMjT RlXl[ RXC]H RXCRJ RaGaK RhFgI RbT`Y\\[ R]XRZ RTQUV',
	    'Po]ElE RTJ[J R^MkM RSO\\O R_PjP R]TlT RXJXY R[QZV R^H^M RbEbM RgEgM R^HkHkM RhVlZ RXC\\H RWCVGRJ RdTd[a[ R`V\\YRY RTQUV',
	    'Po^GlG RTJZJ R_MlM RRO[O RcSiS RWJWY RcIcS RcVcZ RdCeG RiIiS RWD[H RWCVFRJ RTRTW RgVgZ RjVlZ RZRYV R`V_[ R[XRY R^G^S]Y',
	    'Po]HlH RTJ[J R_KjK RSO\\O R_QkQ R]TlT RXJXY R_N_Q R_E_K R_EjEkK R_NkNkQ RXC\\H RVFRJ RWCUH RTQUV ReCeQbT`Wk[ R[QZV R\\XSZ RjTfY\\[',
	    'PoTJ[J RbJlJ RSO\\O RcUkU RcZkZ RXJXY R[QZV R^F^I R`L`[ RcOc[ ReCeF RgKeOkOk[ R^FlFlI RXC\\H RVFRJ RWCUH RaIaM]R RTQUV R\\XRY',
	    'PoTJZJ RSO[O RfSkS RWJWY RZQYV R_L_[ RWC[G RcHeJ RhHkK RTQTV RbSdY RVCSI R_C]I R`I\\P RiEfL RdDbL R[XRY RgNfZl[ RbPaZ',
	    'PoSE^E RTH^H RTN^N RZSdS RWVhV RS[l[ RYCYH RYJYN RTJ^J^N R_S_[ RZX[Z RdXcZ RbKeNkQ RaJiJfM`P R_OeRlT RbEaI R_PXSRU RbDhEhIkH RTJSR',
	    'PoRE^E RbJlJ RSJ^J RSL^L RRO^O RZTdT RVWhW RR[l[ RSGSL RXCXQ RSG^G^L R_T_[ RgIgQ RZXZZ ReXcZ R_PdSlU R_PYTRU RbEbJaO RjDaE',
	    'Po[FcF RTJYJ R\\NcN RROZO R\\ScS R[VcV RSRTW RWJWY RYRXV R\\J\\S R_C_[ R\\JcJcS RfLkLj[ RWDZH RUFRJ RVCTH RZXRZ RkDfFeSdZ',
	    'Po^FlF RTJ[J R_LkL RRO\\O R_PkP R`VjV R`ZjZ RXJXY R_I_P R`S`[ RcCcP RgCgP R`SjSj[ R_IkIkP RXD\\H RWDRJ RTRTW R[RZV R\\XRZ',
	    'Po^HeH RTJZJ RSO[O R]SlS RWJWY RbDbO RdQd[ RhEhL RdSjY RWD[H RaIeM RdS^Y RWERJ RbH]N RTRTW RZRYV RkCkPhP RdD^D R[XRY',
	    'PodFkF RTJZJ RaKhK RdNkN RRO[O R`VlV Rb[f[ RTRTW RWJWY ReCeN RaQgQf[ RWD[I RWCVGRJ RbSbU R^IkHkK RiRhU RZRYV R[XRY R^H^T]Z RjPdQ',
	    'Po\\JlJ RTJZJ RROZO Ra[h[ RSRTW RWJWY RbTb[ R^EdEdP RhXh[ RcRfU RWEZH RdJkQ RdJ]Q RiFhI RVFRJ R_G`I RiTlZ RVCTH R^U\\Z RYRYW RZXRZ RjD`E',
	    'Po^FlF RTJYJ R_MlM RROZO R`QjQ Rb[e[ RWJWY RYRYW ReEe[ RaJjJjQ ReTjY R`SbU RWD[H RjSgV RUFRJ RSRTW RVCTH ReV_Y RZYRY R^F^T\\Z',
	    'Po]GlG RTJZJ RRO[O RWJWY RfUl[ R^PaS RWD[H R^JaM RgPkT RhJkM RaP]T RjIfN RbV][ RjPgS RaI]N RWERJ RTRTV ReCeQhY RdRaX RZRYW R[XRY RdMcU',
	    'Po^HlH RTJZJ RRO[O R]OcO RfOlO R]TcT RfTlT Ra[c[ RWJWY R]KcKcZ ReCeH RlElH RWD[I RWERJ RTRTV RZRYV R^E^H RfKlKl[i[ R[XRY R]K]T\\Z RfKfTeZ',
	    'Po^FkF R]JlJ RTJ[J RSO\\O R_PjP R_SjS R]WlW RXJXY R[QZV R_M_S RdCeF RdSd[ R_MjMjS RXC\\H RVFRJ RaFaJ RWCUH RhFhI R\\XRY RTQUV',
	    'Po\\FkF R_IfI RTJYJ RROZO R_OhO R^TjT R\\WlW RTRTV RWJWY R\\K\\O RdQd[ R\\KkKkO RWD[H RfMiP RgH_P R_R\\U RWCVGRJ RdE`I RZQYV RZXRY',
	    'PoTJ[J RRO\\O R^OlO R`VkV R`ZkZ RWJWY R^G^O R`R`[ ReGeO R`RkRk[ R^GlGlO RWD\\I R`CaF RjCgG RWERJ RTRTW RaIbM RiIhM RZRZV R\\XRZ',
	    'PoTJYJ RROZO R_QaQ RcZjZ RSRTW RWJWY RYRXV R\\O\\Y R\\DaDaK RcSc[ RcSjSj[ RVDZH RfMlR RhLbR RUFRJ RdHgN ReGjGfO RVCTH RfCcJ R\\D\\K`K`W[Y RYYRZ',
	    'Po]FlF RTJ[J R]NlN RSO\\O R]SkS R]WlW RXJXY R[Q[V R`J`S RbCbH RdJd[ RgCgH R^JjJiS RXC\\H RVFRJ RWCUH R\\XRY RTRUV',
	    'Po_GkG R_JkJ RTJZJ R_MkM RSO[O R_PlP R^TlT RWJWY RZQYV R`F`P ReFe[ ReSlZ RWC[G ReS^Y RUGRJ RVCTI R`C^J RTQTV R[XRY RfEeJ',
	    'Po\\IlI RTJZJ R]NkN RSO[O R^QjQ R\\UlU RWJWY RZQZV RbCbI RfCfI RWC[H RjDiG R`J`M R^D_G ReVhYl[ RhJgM RVCTI RdNdV`Y\\[ RTQTV R[XRY',
	    'Po]ElE RTJ[J R^LjL RSO[O R^OjO R]SkS RTRUW RXJXY RZQZV R^H^O RdCdR RbVb[hZh[ R^HjHjO RjWk[ RXC\\H RhQlT RVFRJ RbTfV RWCUH R^V\\ZRZ',
	    'PobJkJ RTJZJ RSO[O RcTkT RbZlZ RWJWY RZQYV RcNcT RgJgZ RcNkNkT RWC[G RjGkJ RTQTV RjVk[ RVCTI RfDcJ R_[\\[ R[XRY R\\DaD`K\\K\\QaPaV_[',
	    'PoTJZJ RSO[O RiQlQ RWJWY RZQYV ReLiLjQ RWC[H R]G`I RfFkK RiEfF RkGhH RiVeZ RUGRJ RTQUV ReCgI ReVhZl[ RdSjShY RVCTI Ra[][ ReLdQ R[XRY ReSeV R^DbDaH\\LbLaP]P]TaTa[',
	    'Po]HlH RTJ[J RSO[O ReQlQ RcWlW RXJXY RZQZV RdCdN ReQeV RiNi[ R^TaU RXC\\H R`DaG RjDgG RaW][ RVFRJ RTQUV RdGhLlM RWCUH R`N]U R_QcR_Y RdG_M]M R\\YRZ',
	    'Po_FdF RTJZJ R^KlK RRO[O RhUjU RWJWY R^O^U RaFaI RdId[ R^OjOjU RdSkZ RWD[H RfFjFjH RdS^Y RWERJ RTRTV R_C]H RZRZW RgCeH R[XRY',
	    'Po\\HlH RTJ[J R^LjL RSO[O R^UkU RTQUV RXJXY RZQZV R^N^XaWa[ RdCd[ RgWg[ R^EjEjL RkNk[ RWC[G RVFRJ RWCUH RgNgRkR RjWfW R[XSZ RaNaR]R\\Z',
	    'Po[GbG RTJYJ R[KcK RSOZO R\\RbR R\\VbV R`[b[ RWJWY RYRYW R\\N\\[ R]C]K RaCaK R\\NbNb[ RVDZH RUFRJ RSRTW RgQjZ RiReZ RVCTH RfMiX RZYRY ReIgU RfIkIgW RfCdO',
	    'PoTJ[J RRO\\O R`PiP R]TlT RWJWY R]H]K R`K`P RdCdH RdPd[ R`KiKiP R]HkHkK RdSjY RWD\\H R_D`G RiDhH RdS^Z RWDRJ RTRTW R[RZW R\\XRZ',
	    'Po^IiI RTJ[KlL RRO\\O R]QiQ R]TiT RXJXX R^E^I RcCcL R]NiNiW R^EiEiI RXD\\I RWERJ RTRTV R]N]WhXl[ R[RZV R`YZ[ R\\XRY',
	    'PoZHcH RTJZJ R]LbL RROZO R]PbP R[TdT RWJWY RYQYV R]C]T RbCbT ReLkLj[ RaVbY RVCZG RTRTV RVCSJ R]V[[ R[XRY RkDeEeSdZ',
	    'Po]DlD R^HbH RTJ[J RfMkM R^NbN RSO\\O R^UkU R^ZkZ RXJXY R[QZV R^H^M R^Q^[ RbDbQ RdQdZ RfDfQ R^QkQk[ RfHkHkM RXC\\H RVFRJ RWCUH RTQUV R\\XRZ',
	    'Po^FjF R]JkJ RTJ[J R]McM ReMkM RSO[O RdQlQ R\\TlT Rg[l[ RXJXY RZQZV RdCdI RgTg[ RhKhQ RlXl[ RXC\\H RaW^Z RVFRJ RWCUH RbT`Y RTQUV R`KbQ]Q R\\YRZ',
	    'Po]FlF RaJhJ RTJ[J R]LlL RSO\\O R_SkS R_VkV RXJXY RZQZV R_O_V RaCaJ RdLdV RhChJ R_OkOkV RgXkZ RXC\\H RVFRJ RWCUH RbX\\[ RTQUV R\\YRZ',
	    'PobGlG RZTdT RVWhW RR[l[ RTHTO RVJVN RYCYO RTH^H^O R_T_Z RTDVF ReXb[ R^D\\F R\\J\\M RcJgLjN RcCaI RiHeLaN R_PeRlT R^QXSRT',
	    'Po\\FkF RVWiW RS[k[ RTDTP R_T_[ RYX[[ ReXd[ RYJZM ReKkN RTEZEXI RcI]L RiHeK]O RXTcRlU RgDeFbG\\I RZOWO R_PZSRU RdIeMePbP',
	    'PoTJ[J R]JlJ RSO\\O R^VjV R^ZjZ RXJXY R^R^[ R^EdEeP RdRdZ R^RjRj[ RXC\\H R_F`I RdJkP RiFhI RVFRJ RdJ]Q RWCUH R[QZV RTQUV R\\XRY RiC`D',
	    'Po\\HlH RTJZJ R`OhO RSO[O RaXgX Rg[k[ RWJWY RZQZV R]Q][ R`J`O RaTaY RaTgTgX R_KhKhO R]QkQk[ RWC[G RbG]L RfHhKlL RVCTI R]EcEaJ RTQTV R[XRY RhC`D',
	    'Po^HcH RfHkH RTJ[J RfLkL RSO[O R]QkQ R]VlV RTQUV RXJXY RZQZV RaNaV R^DcDcH RfDfL RgNgV RfDkDkH RgXkZ RXC\\G RVFRJ RWCUH RbX\\[ R\\XRZ R^D^LcKcL',
	    'PocHkH RTJZJ RcKkK RaOlO RROZO RWJWY R\\D\\[ RcDcK RcDkDkK RVEZH ReTbX RUFRJ RTRTW RfRdW RcRkRfZ RVCTH RZRYV RdObT Rk[h[ R\\E`E^L`P`T]T RZXRY RlRk[',
	    'PoaHjH RTJZJ RaLjL R`OlO RROZO Rg[j[ RWJWY RaDaK RaDjDjK R[K^N RWD[H R\\D_G RcU_Y RUFRJ RTRTW RdRbW RhRd[ RVCTH RcO`T RYRYW R]SZYRZ RaSkRj[',
	    'PoTJZJ R^MkM RROZO RWJWY RYRYV R^H^M R]IkIkM RWD[H RVFRJ RaE\\J RTRTV RhRkY RVCTH RdLbP]R RdR\\U RcOfTcV\\Y R`FgEeIdN RZXRY Re[a[ RfUeZ RjQeRfX',
	    'PoTJ[J RSO\\O RfSkS RbYhY RTQUV RXJXY R[QZV R^O^[ RbUbZ RfIfN RfOfS RbUhUhY RXC\\H R^IlIiL RVFRJ RWCUH RcOaS R_DjDeG RcJ]M RbFhG R^OkOk[h[ R\\XRY',
	    'Po^IlI RTJ[J R_LkL RSO\\O R_PkP R_TlT RXJXY R[QZV R`G`T RbWb[ ReGeT RXC\\H RVFRJ ReVf[ RjVl[ RWCUH RaC^J RfDeJ R^V\\[ RTQUV R\\XSZ',
	    'PoTJ[J RfNjN RSO\\O R^PjP R^SjS R\\UlU Rd[g[ RXJXY R^J^S R`GgGfM RgSg[ R^JjJjS R_WaY RXC\\G RcEgDkI RbGbL`N RVFRJ R`E\\I RWCUH RTQUV R[QZV R\\YRZ',
	    'Po]FlF RTJ[J RSO\\O R_PiP R_TiT Ra[d[ RXJXY R`K`S RdTd[ R^MiLiS RhVkZ RXC\\H RfHkN RfFgJ RjHhJ RVFRJ RbH]N RWCUH RdCaJ R[QZV RTQUV R`V\\YRY',
	    'PocHlH RTJZJ RdNlN RROZO RdZkZ RTRTV RWJWY RYRYV RbUbY RdRd[ RhChN RdRkRk[ RWDZH RVFRJ RVCTH R_D\\L RaH\\R R_U_Z R\\U\\[ RZXRZ R`L[L RaObR[R',
	    'PoTJZJ RRO[O Re[i[ RWJWY R]N]Q R]NlNlQ RWD[H R`HcK RhHlL RbV^Z RVERJ RTRTV RkEjH R^E^H RcEcH ReEeH R_K\\L RZRYV R[XRY RaC`J RdPbX RhCgJ R]SjSi[',
	    'Po\\FdF RfIlI RTJZJ R]NdN RSO[O R]VcV R^[`[ RTQUV RWJWY RZQYV R]I]M R`S`[ R`C`F RWC[H R]QcQ`T RgVb[ RUGRJ RgRk[ RiQfX RVCTI R]IcIcM RfNiY RfJhW RkIhU RfCdO R[XRY',
	    'Po^DlD RTJ\\J R^LkL RSO]O R_QkQ R_TkT R^WlW RXJXY R^H^L R_N_T RcDcL ReTe[ RgDgL R_NkNkT R^HkHkL RXC]H RWFRJ RWCUG RTQUV R[Q[V R\\XRZ',
	    'Po\\JcJ ReJlJ RTJZJ RSO[O R^VjV R^ZjZ RWJWY RZQZV R^E^J R^R^[ ReEeJ R^RjRj[ RWC[H RVCTI R^O\\P R^EbEbN`N RfOdP RTQTV RiPlO ReEjEgN R[XSZ',
	    'PoTJ[J ReNkN R\\NcN RSO[O R^QlQ RTQUV RXJXY RZQZV RlXl[ R_TbW RWC\\G`J RjGhK ReGhJeN RVFRJ R`K]N RbKcN RjKkN RgOjP RhZk[ RWCUH R`C^H RbG_L RhCfH RjTfX`[ RdPgY R[XRZ R^Q]Z',
	    'Po^HcH RgHlH RTJ[J R^LcL RgLlL RSO[O RbRhR RbVhV ReZhZ Ri[l[ RXJXY R[QZV R^E^[ R^EcEcL RgEgL RbOhOhZ RgElEl[ RXC\\H RVFRJ RWCUH RTQUV R\\XRY RbObUaZ',
	    'Po^FkF RTJ\\J R]JlJ RSO]O R_PkP R_TkT R^WkW R\\[l[ RXJXZ R[R[W R_M_T RdCdF RdMd[ R_MkMkT RXC]H RXCRJ RTRUW RaGaJ RiGgJ R]YR[',
	    'PoTJ[J RaLgL RSO\\O R_UiU R\\[l[ RXJXZ R_O_U R_OiOiU RXC\\H RgIlM RiEgG RkGhJ RVGRJ R]G`I\\M RTRUW R`VaY RfDiK RhVfZ RWCUH R^EbE_K R[RZW R\\YRZ',
	    'Po]ElE RTJ[J RSO\\O R]RlR RXJXY R[QZV RbMbR RgMgR RXC\\H RVFRJ RjVlZ RbSfT RWCUH RbUb[hZh[ R^I^MbM RbEbI^I R_V]YRZ RTRUW RgEgIkIkMfM',
	    'Po^HkH RTJ[J R^KkK R]NlN RSO[O R_UjU RWJWY R^D^K R_Q_U RdUd[ R_QjQjU R^DkDkK RhWlZ RWD[H R`W]Z RWERJ RZRZV Ra[d[a[ R[XRY RTRTW',
	    'PoRE_E RRN^N RZSdS RUWhW RS[k[ R_S_[ RYXZ[ RfKjP ReXc[ RcJgK RcC`J RbGkGhKdNaO RUIXI R`QlT RTGTH]G]IYI RTK^KXMYQVP R_OZRRT',
	    'Po]ElE RTJYJ R_KeK RROZO R`TeT RVJVY RYRXV R`M`T ReMeT RgKkKkP RVDZH RVERI RTRTW R`GaJ RZXRY RiOjY R]E]T[[ RdGcKbTaY RgGfP RiNgX',
	    'Po^GkG RTJZJ R]KkK R]McM RROZO ReQkQ R\\TlT RTRTW RWJWY ReLeQ R^DkDkJ ReLkLkQ R`VcY RWD[I RVFRJ RVCTH R`M`Q RYRYW R[XRZ RhRh[d[ RcQ]R',
	    'Po]FcF ReFlF RTJZJ R\\JcJ ReJlJ RRO[O R^VjV R^ZjZ RWJWY RZQZV R^R^[ R^RjRj[ RWC[H R`C`LbN RhChJjO RVCTI ReOdO RTQTV R[XRY R`F_N RhFgN',
	    'Po]GkG RTJ[J R]NlN RRO\\O R_UiU R_YiY RWJWY R_Q_[ RbFbN RgFgN R_QjQj[ RWD\\H R^I`K RjIiL RWERJ RTRTW RZRZV R[XRY',
	    'Po]FdF RTJZJ R[JlJ RSO[O R]RdR RWJWY RZQYV R]M]R R^T^W RaCaJ R]MdMdR RWC\\G RjDkH RVFRJ RlWj[k[ RcScW RWCUH RTQTV R[XRZ RgCgMgPhZ RjNgVdY\\Y',
	    'PocGlG RSG[G RZSdS RVViV RS[k[ RWCWP R_S_[ RgCgO RWH[L RZX[[ R^KaN RgHlL RfIbM ReXd[ RUJRN RaD\\H RaJ\\N R\\EaG RWFTL RgEdL R`QlT R`OZRRT',
	    'PoTJZJ RRO[O R^TkT R]WlW Ra[e[ RWJWY RYRYW R]G]K RdCdG R^PfPe[ R_IiIiM R]GlGlK RWD[H R_D`G RjDgG RVFRJ RVCTH RTRTW R[XRZ R_I_MiNcP',
	    'PoaGiG RTJZJ RaJiJ RaNiN RRO[O RfQlQ R\\TcT RTRTW RWJWY RaDaN R^F^QcQc[ RfQf[ RaDiDiN RlFlP RfTlTl[ RWE[I RWCUGRJ RZRYW R^T\\Z R[XRZ',
	    'Po^GkG R_JkJ RTJ[J R^MlM RRO\\O R]RlR R`ZjZ RWJWY R`U`[ ReGeR R`UjUj[ RWC\\H RaDbG RVFRJ RhCgG RaNaR RiOhR RWCTH R[QZV R\\XRZ RTRTW',
	    'Po]HcH RfHlH RTJZJ R]KcK RfKlK RSO[O R_SjS Rh[l[ RWJWY R]E][ R]EcEcK RfEfK R`NhNfY RfElEl[ RWEZH RVFRJ RWCUH RYRYW RZXSZ RTRTW RbOaW',
	    'Po]IcI RfIlI RTJZJ R]McM RfMlM RRO[O RaShS RaWhW Rh[l[ RWJWY RZRYV R]D][ RaOaW R]DcDcM RfDfM RaOhOhW RfDlDl[ RWD[H RVFRJ RTRTW R[XRY',
	    'Po^HkH RTJZJ RSO[O R_QjQ R_TjT RWJWY RZRYV RfCfM R]OjOjW R]EkEkH RWD[H RWCVFRJ RTRUV RbCbK^N_WiXl[ RbY][ R^H]KlKkMhM R[XRY',
	    'PoaHlH RTJYJ RROZO Rb[l[ RWJWY RhOhX RWD[H RgPkS RfHaM R\\M^M^X[[ RUFRJ R\\E^I RcCdG R^Xd[ RVCTH RjChH RfMcP`Q RkKgPdT`V RSRTW RYRYW RgYdY R^M[M RZYRZ',
	    'PodEkE RTJZJ RaKgK RdMkM RRO[O R^RlR RaUjU Re[i[ RWJWY RZQYV RaOaT RdCdM RaOjOjT RWC\\H R^HlHkJ R_WjWi[ RVCTI RdY_[ RTQTV R[XRZ R^H^S\\Z ReOeTdY',
	    'Po_GlG R_JkJ RUJ[J R_MkM RSO\\O R_PlP RcWgW RXJXY R^S^[ R`F`P R^SbScW ReEeP RgSgW RXC\\G RVFRJ RWCUH ReCeH R[QZV R`D_K RgSlSl[h[ RTRUV R\\XRY',
	    'PoeFjF R\\IlI RTJZJ R]MlM RRO[O R_PeP RWJWY R_E_I ReCeI RlXl[ RWEZH RbPcW_Z RhVc[ RVFRJ RTRTW RhKjL RdSbX RjPgX RWCUH RYRYV R]M^U^W RZXRY RfLhY R]P\\Z',
	    'PoTJ[J R_LiL RRO[O R]UlU RWJWY RZRZV R^F^R RdHdQ R^FjFjR RWD[I RfMgQ RaHaJ RWERJ RcCcF RTRTV RdTeWgYl[ RbLaP RgHfJ RdSbW`Y[[ R[XRY',
	    'Po]FlF RTJZJ R]KlK RSO[O R]QlQ R^TjT RTQTV RWJWY RZQZV RbHb[ RdCdF RfIf[ R^MjMjT ReTkZ RWC[G RaV]Z RUFRJ RVCTI RbS_X RdH^H RjHdH R\\XSY R]F]S[Z',
	    'Po\\FlF RTJZJ R^KhK R]OlO RRO[O R`WjW R`ZjZ RWJWY RZQYV RaSa[ RaCaH RcHcO RgCgH R_TjSj[ RWC[H RfQiQ RVCTI RkIbQ\\T RTQTV R\\XSZ',
	    'Po`IiI RTJ[J RRO\\O R^PcP RfPkP R]SlS RTRTW RWJWY R^K^P R`D`I R^KcKcP RdQd[ RfKfP R`DiDiI RfKkKkP RdSkY RWD\\I RdS^Y RWERJ RZRZW R[XRY',
	    'Po`JhJ RTJ[J RSO\\O R^SbS RfSjS RXJXY R[QZV R^M^R R^McMcR RfMfR RfMjMjR RgWk[ R`WbY RXC\\H RdClK R_W[[ RWCUGRJ RcE]K RgTeZ R`T^Y RTQUV R\\XRY',
	    'Po^IlI RUJ[J RSO\\O R_UhU R^YhY Rg[k[ RXJXY R^D^I R_Q_U RdNdY RgDgI R_QhQhU R^DlDlI RgWhY RXC\\H RVFRJ RWCUH RcDbI_O R[Q[V R\\XSZ RTQUV RcH`MlMlVk[',
	    'Po^EkE RTJ[J R_KbK RfKjK R^NbN RfNkN RSO\\O R^VkV R^ZkZ RXJXY R[QZV R]H]K R^R^[ RdEdP RdRdZ R^RkRk[ R]HlHlK RXC\\G RVFRJ RWCUH RTQUV R\\XRY',
	    'PodGlG RTJZJ R]LbL RdNlN RSO[O RdTlT R^XcX RWJWY RZQZV R]E]R^[ R]EbEbK R^QcQcX ReIfM RhChG RhNh[ RWC\\H RUGRJ RjIjM RVCTI RTQTV R[XSZ R]O[Z',
	    'Po]FdF RTJZJ R[JlJ RSO[O R]QdQ R\\SeS R]VdV R\\ZbZ RWJWY RZQYV R]L]P RaCaJ RaSaY R]LdLdP RlWl[ RWC\\G RjDkH RVFRJ RWCUH RTQTV R[XRZ RgCgNgQhZ RkNgWdZ_Y',
	    'Po^IjI RTJ[J R]LlL RSO\\O R_RjR RXJXY R^D^I R_N_R RaUaZ RbDbI RfDfI R_NjNjR R^DjDjI RfVj[ RXC\\H RkUgW RVFRJ ReRgX RWCUH RcT\\W R[QZV RTQUV R\\XSZ RfY^Z',
	    'Po\\ElE RTJ[J R]JlJ RcNhN RRO[O R_QjQ R\\TlT RWJWY RZRZW R_H_P RaCaG RcHcN RdQd[ RgCgG RhHhN RWD[I RdS^Y RWERJ RTRTW RdShWlY R[XRZ',
	    'Po^IkI RTJ[J R_LkL RSO\\O R]PlP R]TlT R^WkW RXJXY R^D^I RbDbI ReIeO ReTe[ RgDgI R^DkDkI RXC\\H RWCVGRJ RbQaT RhQgT RTQUV R[QZV R\\XRY',
	    'PoTJZJ RROZO RcUhU Ra[l[ RTRTW RWJWY RYRYW R`M`X RcPcV RfHfM RcPhPhU RaDiDiM R[M^M^W`Z RWD[H RUFRJ R\\E^I RVCTI RaHfH R]XZYRZ RaDaMkMkXhX',
	    'PoUJ[J RSO\\O R`OjO R^VkV R^ZkZ RXJXY R[QZV R^G^K R^R^[ R`K`O ReCeG ReReZ R`KjKjO R^RkRl[ R^GlGlK RXC\\H R`DaF RjDiF RWFRJ RWCUH RTQUV R\\XRY',
	    'PodEjE RTJ[J RdNkN RSO[O R_PkP RWJWY RZQZV RdCdM R^HkHkK RgUlY RWC\\H RjRgU RVFRJ RePiW RWCUH RdQ_S RcU_V ReUcX^Z ReTfXe[b[ RTQUV R\\YRZ R^H^S\\Z RhJ_K',
	    'Po^FkF R^JlJ RTJ[J RRO\\O R_PkP R_SkS R_WkW RXJXY R_M_W ReCeH RiHiM RaH`MkMkW RXD\\H RWDRJ RTRTV RgXlZ R[QZV RbY][ R\\XRZ',
	    'PoaFjF R`JkJ RTJYJ R`NkN RSOZO RaRjR R`UlU Rb[l[ RWJWY RZQYV RfCfJ RfNfY R\\K^M RVC[G R\\D^G RiKhM R\\Q^Q^X[[ RbKcM RVCSJ RTQTV RZXRY R^Q[Q',
	    'Po_FkF RTH\\H R^JlJ R_MkM RZSdS RVVhV RS[k[ RTE\\E\\H RUK]K]O R_S_[ ReJeP ReCeF RYX[[ RhGhJ ReXc[ RbGbJ R_OWS R^PaPlT RTEULUO]PRT RTHSO',
	    'Po^FkF RTJ[J R]JlJ RSO\\O R_PiP R_SiS RXJXY R[QZV R_M_S RdCdF R_MjMjS RcUfV RXC\\H RVFRJ RaGaJ RjVkZ RWCUH R^W][ RhGgJ RbVb[hZh[ RTRUW R\\XRZ',
	    'PoTJZJ RRO[O RTRTW RWJWY R\\J\\M R\\JlJlM RWD[H RiMlP RjFhJ RaQ]V RWCUGRJ R^M]P R^F^I RcFcI RbLfL R`TcXgZl[ RYRYV RZXRY R`TiTfWbY[[ RaMaQgPgQ RjC`E',
	    'PoTJ[J R^LjL RSO\\O RXJXY R^E^L RjEjL RfQkPkQ RXC\\G RdJeK ReHhJ RgGiH RbN^Q RcQ^W RVFRJ RgDaJ RaTdXk[ R`FfH RWCUH RfMfQ R[QZV RaTiTfWbY\\[ RTQUV R\\XRY',
	    'Po]GlG RTJ[J RRO[O R^OkO R^RkR R]UlU RTQTV RWJWY RZQZV R^J^O RbXb[ R^EeDeU R^JkJkO RiWl[ RWC\\H RUFRJ ReWf[ RaLaN RhLgN RVCTH R^W\\[ R[XRY RiCbD',
	    'PoTJ[J RSO[O R_PlP R_SkS R_VkV R_ZlZ RWJWY RZQZV R_O_[ R^EcEcL RfNfZ RfEkElL RWC\\H RhEfH RjHfK RUGRJ R`E^H RbH^L RVCTI RTQUV R\\XSZ',
	    'Po]EkE R^HjH RTJZJ R_MiM RSO[O R]PjP R\\RlR RcUlU R]YbY RTQTV RWJWY RZQZV R]T]Z R]TbTbY RdCdH RdMdP RdWfY RWC\\G RUFRJ R\\KlKkM RVCTH RiSi[f[ R[XRZ',
	    'PoTJ[J RSO[O R]SlS RTQUV RXJXY R[QZV R^N^Q R^E^H RdPd[ R^NkNkQ RdSlZ RXC\\H R`HcK RhHlL RkEjH RcT]Z RVFRJ RWCUH RcEbH RfEeH RfKdL RaC`K RhCgK R\\XRZ',
	    'PofIlI R\\JdJ RTJZJ RROZO R\\OdO R\\TdT Rg[j[ RWJWY RYRXV R\\E]H R^K^N R_D_J R`O`Y RbDbJ RjDj[ RWD[H RUFRJ RTRTV RVCTH RdEdH RbKbO RgOhU RZXRZ RdX[Y',
	    'Po\\FlF RTJYJ RROZO R^WjW RWJWY R\\JaJaO ReIeO RgJgO R_RjQj[ RWD[H RcCdF RfGdJ RUFRJ RTRTW RVCTH RkIfI RjOfO RjIkO RYRYV R^J]O RZXRY R_P_U]Y',
	    'Po\\ElE RTJZJ RSO[O Rg[k[ RTQTV RWJWY RZQYV R]I][ RdEd[ R]JkJk[ R_TbW RfTiW RWC[H RfNiP RhGlJ RUGRJ R`F\\J RhMgQ RhSgW RVCTH R_MaN`Q RaS_W R[XRZ',
	    'Po\\FlF R^JjJ R\\MlM RRPZP RcRjR R^RfR R\\[l[ RTKXKWZ R^U^[ RaCaH RbUb[ RfUf[ RgCgH R^UjUj[ RWD[H RhOkS RdHdM^S RVFRJ RWCUH RYRYW RTSTW RZYR[',
	    'Po]ElE RTJ[J R^KkK R_MjM RSO\\O R_OjO R_RlR RXJXY R[QZV R`I`R RaCaG ReIeR RgCgG RXC\\H RVFRJ RWCUH R_H]M RaWfYl[ R]UjUgWcY\\[ RTQUV R\\XRY',
	    'Po`EkE RSE^E RTJ^J RaLkL RTL^L RSN^N RaOkO RZSdS RVViV RS[k[ RTGTL RYCYP RTG^G^L R_S_[ RYX[[ RaHbJ RdHfJ RiHkJ RjFiH ReXc[ RbFaH RfFdH RfLfO R`QlT R_OZRRT',
	    'Po`HiH RTJ[J RSO\\O R_RjR R_UjU R_WjW RXJXY R[QZV R`O`W RdCdF RdHdM R]OjOkW R^F^GkFkH RXC\\H RVFRJ R`J^M RgYl[ RWCUH RiK_O RbY\\[ RTQUV R\\XRY',
	    'PoTG\\G RaGjG RTI\\I RaIjI RTL\\L RaLjL RZSdS RVViV RS[k[ RTDTL RTD\\D\\L R_S_[ RaDaL RaDjDjL RZM\\O RYX[[ RhNkP RdNbO ReXc[ RVNSP R`PlS R_OZRRT',
	    'Po]ElE R`JjJ RTKZK R_LlL RRP[P R`QjQ R`SjS R`VjV R][l[ RWKWZ RYRYW R]E][ R`O`V ReFeL R`H`IjHjJ R`OjOjV RWD[I RUGRJ RTSTX RVCTI RcX_X R[YR[ RhXlX',
	    'PoTJZJ RaLgL RSO[O R\\RlR R\\UkU Ra[d[ RWJWY R^N^R RaIaL RbNbR RdUd[ RfNfR R\\FkFkI RWD[H RcGeJ RcDeF RiIkL R\\F]I]L RWERJ RZRYW RTRTW R^NjNjR R[XRZ',
	    'Po^ElE RTJZJ R_JbJ RgJjJ R_MbM RgMjM RRO[O R]PlP Ri[l[ RWJWY R^T^[ RcSc[ RdEdN RgTg[ R^TlTl[ R]H]IlHlJ RWD[H RWCVFRJ RTRTV RZRYV RcPbW R[XRY',
	    'Po^DdD RgIlI RTJ[J R^LdL RSO[O RfOlO R^PdP R\\[l[ RTRUW RXJXZ R[RZW R^D^P R^S^[ RbSb[ R^HdHdL RfSf[ R^SkSk[ RXC\\H RWCRJ RaLaP RaDaH RgDfK R\\YR[',
	    'PoSE^E RaFlF RbHjH RSL]L RaOkO RVVhV RR[l[ RYEYH RYLYO RSH^H^K R_T_[ RaKaO RdKdO RaKkKkO RZX[Z RdXcZ RSESP^OiT RbC`I R^PTU RhKhO RZTcSlU',
	    'PoaIjI RTJZJ R`KlK RSO[O RbQjQ RbXjX Rb[l[ RTRTV RWJWY RZQZV RbNbX RfCfK RbNjNjR RaEjEjI RbTjTjX R]J_L RWC\\H R]D_F R\\P_P_X[[ RVFRJ RWCUH RaEaI R[XRZ',
	    'PoTJYJ RROZO RaPgP R]TkT Ra[d[ RWJWY RYRYW RdTd[ R]F]GlFlH R^N`O RVEZH RhVkY RgMlR RcCdF R_LbM\\S ReJkJiN R_W\\Z RUGRJ R_J\\M ReIhO RVCTI R_JcJaO RSRTW RZXRZ',
	    'PofHlH RTJ[J RSO\\O R^QjQ R^TjT R^WjW RXJXY R^N^W R^HcHaL RiHiL R^NjNjW RXC\\G RWCVGRJ RgYl[ RaY[[ RTQUV R[QZV R\\XRY RcD^E]L RkDfEeL',
	    'Po]FlF RTJZJ RRO[O R^RlR RWJWY RbCbH RbIbN RfIfN RhChH R^IkIkN RlXk[ R_UbW RWD[I RWERJ RSRTW R^I^NiOjP RhZk[ RZRYV RjTeYb[ R[XRY RdNfX R^S]Z',
	    'Po\\HcH RTJYJ RROZO Ra[d[ RTRTW RWJWY RYRXV R`D`O RdQd[ R_SaU RWD[H R_HcL RiSeW RUFRJ ReOlS RdWkZ RVCTH R`H]M RfCdI RiGfM RdMaQ\\S RdW\\Z RjOhO RZXRZ RcD\\D ReGlGkO',
	    'Po^IjI RTJZJ R^MjM RRO[O R\\UcU ReUlU R\\ZcZ ReZlZ RWJWY R\\P\\[ R^E^M R_P_Z R\\PcPc[ RdEdM RePe[ RiPiZ R^EjEjM RePlPl[ RWD[H RVFRJ RTRTW RWCUH RYRYW R[XRZ',
	    'PoeGlG R]GdG RTJZJ RhMlM RSO[O R^PdP R^RkR R^WjW R^ZjZ RWJWY RZQZV R^N^R R^U^[ RaDaM RiHiM R^UjUj[ RWC[G R`GcK RjDkE R`H\\L RVCTI RTQTV RcD]D RhDfL R[XSZ RiO`P',
	    'Po^FcF ReFkF R]IcI ReIlI RTJ[J RSO\\O R_QjQ R_TjT R_WjW RXJXY R[QZV R_N_W R_NjNjW RXC\\G RaC`JcL RiJlM RVFRJ R`J]M RWCUH RhGjL RgYl[ RfLdM RaY[[ RTQUV R\\XRY RhDgK',
	    'Po^FlF R^IlI RTJ[J RcLiL R^NlN RSO\\O R`TkT R`WkW RTQUV RXJXY RZQZV R`Q`W RcFcL ReCeF RfNfW RiFiL R`QkQkW RXC\\H RVFRJ RhYl[ RWCUH RcY_[ R[XRY R^F^R\\Z',
	    'PoTJZJ R_MiM RRO[O R^RjR R^TjT R^WjW RWJWY RZQYV R\\G\\J R^O^W R_J_M RdCdG R_JiJiM R^OjOjW R\\GkGkJ RWC[G R_D`F RiDgG RfYl[ RVCSI RaY[[ RTQTV R[XRZ',
	    'Po\\FlF RSOZO RaQjQ RbSiS RaVkV Rc[l[ RTJXJVY RaCaH RfOfY RgCgH RVEZH R\\O^O^X[[ RdHaK R]I^L RcJiJeLkN R^Xd[ RVCTI RhKaO RTRTW RYRXW RZXRZ R^O\\O',
	    'PoTJ[J RSO[O R^PkP RTQUV RXJXY RZQZV R^J^P R^R^Z RdRdZ RjRjZ R^JkJkP RWC\\H R^F`H RcFeI RhFkI RjChF RVFRJ R`C]F ReCbF RaLhO RfShT R`SbS ReWhW R`VbV RWCUH RhK`O RbZ\\Z R[XRZ RhZbZ',
	    'Po\\ElE R]HkH RTJZJ RfLkL R]NkN RSO[O R^SjS R^UjU R^XjX RWJWY RZQZV R]J]N R^P^X RdCdG ReJeL R^PjPjX R]JkJkN RWC[H RcJ`L RfYl[ RVCTI RaY[[ RTQTV R[XSZ',
	    'PoTJZJ RSO[O RgPlP R\\SlS RWJWY RZQYV R\\PaPePd[ RdCbFfFfO RdSjY R\\H^K RWC[G RgHiK RcS]Z RbFaN`Q RkLlQ RkGgP RVCTI R^D]I R`H\\P RiDhI RTQTV RbKfK R[XRY',
	    'PodFjF RTJZJ R\\JlJ RRO[O R]RkR R]ZkZ RWJWY RZQYV R]L][ R_E_J RdCdJ RkLk[ RgUiX RaUcW RaMcO RWC[G RgNiP RTQTV RfLeP RfTeX RVCSI RaL_P RaT_X R[XRY',
	    'PodEjE RTJ[J RaKhK RdMkM RSO[O RaRjR RaTjT Rc[h[ RTQUV RXJXY RZQZV RaOaT RcXc[ RdCdG ReOeT RaOjOjT R^HkHkJ RdVgW RiXlZ RXC\\H R`W_[ RVFRJ RdIdM RWCUH R\\XRZ R^H^S]Z',
	    'Po^FlF RTJ[J R^MkM RSO[O R`PdP RTQUV RXJXY RZQZV R`N`U RbFbM RcWc[ ReCdF RfFfM R^IkIkM RgNfUkTkU RWC\\H RVFRJ RfWg[ RjWl[ RWCUH R`W^[ RkPfR RdT^T R\\XRZ R^F^R\\Y',
	    'Po^GjG R^JjJ RTJZJ R]MkM RSO[O R\\QlQ Ra[d[ RWJWY RYRYV R^D^J RdSd[ R^DjDjJ RWD[H RgJgQkU RaJaQ]U RhUfW RVCUGRJ RTRTW RdVkY RdV\\Y RZXRY',
	    'PoTJ[J R^KjK RSO[O R_WjW R_ZjZ RWJWY R_T_[ RbPbR ReHeN R_TjTj[ R]IkHkN RWD[H R_G^NjOlR R^P]R RWDRJ RaD\\I R`EgEdH RTRTW RfPfR RZRZV R[XRY',
	    'Po\\DcD R\\IcI RTJZJ RROZO R^QiQ R^TiT R^WiW RWJWY R^N^W R`D`F R`I`L R\\FcGcI R\\D\\L`MjNjW RWD[H RfGkL RkGeL RWCUGRJ RTRTW ReDhJ RfXl[ ReDkDiJ RaY[[ RYRYV RZXRY',
	    'Po_JiJ RTJZJ RSO[O R_PjP R_RjR R_UjU RWJWY RZRYV R]F]H R_M_U RbHbK RgHgK R_MjMjU R]FkFkI RgUf[lZl[ RhWjY RWD[I RcDeF RWCVFRJ RbUaY][ RTRUW R[XSZ',
	    'PoeFlF R\\FcF RTJYJ R[LcL ReNkN RROZO ReQkQ R\\SbS ReTjT R\\VbV ReWkW Re[l[ RTRTV RVJVY RYQYV R\\O\\[ R]H]K R_C_G ReNe[ ReCeIkIkN RVCZG RVCSJ RaHaK RZXRY R\\ObOb[`[',
	    'Po]EkE RTKZK R^MaM RfMjM RRP[P R\\PlP R^XbX RfXjX R\\[l[ RWKWZ R^S^X R^H^M R^HaHbM R^SbSbX RdEd[ RfHfM RfSfX RjHjM RfSjSjX RWE[I RVFRJ RWCTI RYRYW RjHfH R[YRZ RTSTW',
	    'PodEkE RTJ[J RaKhK RdMjM RSN[N RaQjQ RaSjS R^ZlZ RXJXY R[QZV R`V`Z RaNaS RcVdZ RdCdH ReNeS RgVgZ RaNjNjS R^HlHkJ RXC\\H RWCUGRJ RdIdM R`VkVkZ RTQUV R]XRY R^H^S]Z',
	    'PoZGdG RVJgJ RTNjN RUS\\S RaSiS RSV]V R_VkV R_[l[ RXSXZ R_G_N ReSe[ RXO\\Q RdJcN RiWhZ RUWUY RaWaZ RfPlS R_CjH R[W[Y RWOURRS RcQ^S R_DSI RZKZN R[ZR[ R]ZV[',
	    'Po\\DcD RfGlG R\\JcJ RROZO R^QiQ R^TiT R^ViV RTJXJWY R^O^V R`D`G R`J`M R\\GcGcJ R\\D\\M`NiOiV RfVf[lZl[ RWDZH RgIjL RWCUGRJ RYRYV RgCdI RaV`Y\\[ RSRTV RZXRZ',
	    'Po]HcH RfHlH RTJ[J R]KcK RfKlK R_NjN RSO[O R`UiU Ri[l[ RTRUV RWJWY RZQZV R]D][ R`Q`T R]DcDcK RdLdZ RfDfK R`QiQiT RfDlDl[ RdTiY RWC[G RdU`Y RVFRJ RWCTH R[XSZ',
	    'Po[ElE R_JlJ RTJYJ RSOZO RaOkO RaQkQ R_UlU Rf[h[ RSQTV RVJVY RYQXV RaLaS RaCaG RfHfS RgCgG RiTi[ RaLkLkS R\\N^P RbWdY RVCZG R\\I^K RhHkH RVCSJ R^S\\[ RZXRY',
	    'PoTJZJ R[KlK R\\N_O RSOZO R\\R_R R\\U_U RWJWY RYQYV R_K_Y RVCZG R^F`H RcFeI RiEkH RhVc[ RjPfY RlWk[ RVCSJ R^C\\J RcCaI RTQTV RjNhW ReNbN RbReRbR RbUeUbU RgCgQhZ RZXRY RbKcY[Z',
	    'Po`IhI RTJ[J RSO[O R]VjV RTQUV RXJXY RZQZV R]R][ R]L`L`O RbRb[ RbLfLeO RfRf[ RhLkLkO RWC\\H RdChGlI RVFRJ R`G\\I RcC^H RWCUH R`L]L]O`O]O ReLbLbPeObO RkLhLhPkPgO R]RjRj[h[ R[XRZ',
	    'PoTJZJ R^JjJ R^LcL RSO[O ReOlO R]WjW RTQTV RWJWY RZQZV RfJfO R]HjGjJ R\\TjSjW Re[lZl[ RgXjY RWC[G R_EfEdG RVCTI RaO]S]W RdWe[ RjLeM R_QfQdSaY[[ R[XRZ R`C]G^N RcO\\O',
	    'Po]FlF RTJ[J RfLjL R]NkN RSO[O R]QkQ R\\TlT RTQUV RXJXY R[QZV RaVaZ R^LbLaS RdCdF RgLgS RfHjHjL RXC\\G RVFRJ ReVhZl[ RWCUH RcU\\X RjUfW R^H^L R^HbHcL RfHfL R[XRZ ReY^[',
	    'Po_IiI RTJZJ R^KjK R\\NkN RSO[O R`ThT ReWiW Rc[k[ RWJWY R]F]H RaPaT ReTe[ R_QiQiT R]FkFkH R`XbZ RWD[H RfGfNlS RcCeE RbGbN]S R`W\\[ RWERI R[RZV RTRUV R[XRY',
	    'Po^GkG RTJ[J R_LkL R_OkO RSO\\O R_QlQ RbZhZ Ri[l[ RXJXY R^S^[ R^D^G R_J_R RbWb[ ReIeQ RbWhWhZ RkDkG R^SlSl[ RXD\\H RbU_V RWERJ R`H\\L RfTjV R[RZV RTRTV R\\XSZ RdCdGdI',
	    'Po]ElE RTJ[J R^McM RfMkM RSO[O R_QlQ R_TkT R_WkW R_ZlZ RTQUV RXJXY RZQZV R^I^M R`P`[ RaCaG R^IcIcM ReOeZ RhChG RfIkIkM RXC\\H R`P\\S RVFRJ RWCUH R`M^R RfIfMeQ R\\XRY',
	    'Po\\FlF R_KlK RROZO RaXfX RSRTV RTJXJVY R_K_X RaCaH RaNaX RaNeNdQ RaQfQfU RlXl[ RVEZH RiHkJ RgChIiRfZ RVCSI RaUeUdX RXRXV R\\K\\P_P\\O RYXRY R\\S\\Z RkNhTiZ R[S_S^[',
	    'Po]DkD R`GiG R`JiJ RTJ[J R]MkM RSO[O R^RcR RfRkR R^UcU RfUkU RTQUV RXJXY RZQZV R^O^Y R`D`M R\\OcOc[ RfOfY RiDiN ReOkOk[ RWC\\H RVFRJ RWCUH R[XRZ RbX\\Y RjXdY',
	    'Po^HjH RTKZK R^KjK RRO[O R\\PcP RePlP ReSlS R\\ScS R]WkW R\\[l[ RTRTW RWKWY RYRYW R\\M\\S R^E^K R`M`S R\\McMcS RdTd[ RdEdK ReMeS RhMhS R^EjEjK ReMlMlS RWD[H RVFRJ RVCTI RZYRZ',
	    'PoTJYJ R]NbN ReNjN RROZO R]QjQ RVJVY RZRYV R^K^Q R]S][ RcRc[ RdKjKjQ R\\F\\GkElH RWDZH RcCdE RWERI ReSgT R_SbT ReVgW R_VbV RfHjJ RbG]I RaZ\\[ RhZb[ RiSj[ RcK]K RTRTW RlYl[ RZXRY',
	    'Po]GcG RfGkG R]JcJ RfJkJ RTJZJ R]LcL RfLkL RROZO R^QkQ R^SkS R^WkW R^ZlZ RTRTW RWJWY R]D]L R^P^[ R]DcDcL ReNeZ RfDfL RfDkDkL RWD[H RUFRJ RVCTH R_M\\S RYRYV RZXRY',
	    'PofFlF RTJZJ R\\OkO RROZO R]TjT R\\WlW R`[c[ RWJWY RYRYW R_D_M RdRd[ RiDiL RVDZH R\\F_FaI RbIeL RbEeG RhFlJ RUFRJ ReDbG RhQlR RVCTH R^E\\K RiEgJ ReIbM_Q[R RZYRZ RTRTW RfQ`R',
	    'Po]IkI RTJZJ RdOlO RSO[O RdRlR R\\TbT RdVlV RdZlZ RTRUV RWJWY RZQZV R]D]I R`S`[ RbDbI ReMe[ RfDfI RiMiZ R]DkDkI RaQbT R\\M_P RWC\\H RUGRJ RbUbY RVCTI R_J^N RaM^R RfJcQ R]U\\YSZ',
	    'Po]FcF ReFkF ReHlH RTJ[J RiMlM RSO[O R_QjQ R_TjT R_WjW RWJWY RZQZV R_N_W R_NjNjW RWC[G RVFRJ R^D]G ReDeG RgYl[ RWCUH R`C`H]M RhCgHeL RaY\\[ RTQUV R]HbIbLaL RhFiK R\\YRZ',
	    'Po\\DbD R[FcF R\\IbI R\\KbK RRLZL RcLlL RZUdU RVWhW RR[l[ RWKWR R_U_[ R\\MbMbP RhKhR RXIZL RjIlL RdFgHdK RdXc[ RUCTGVISK RYMYP RjMkQ RXEVI RSMSQ RfCeG ReMdQ R\\M\\P`QcSlU R`PYTRV',
	    'PoRF_F RSI_I RRK_K RZUdU RVXhX RR[l[ RWCWF RYIYQ R_U_[ RbDiDiIlHlI ReXc[ RbMkQ R[C[F[I R]D]F RbKjKfO`R RWMSMTR`RlV RTOVOSO R^O[O R[M^M_R\\SRV RbDbI',
	    'Po`EdE RfKlK R_LdL R]NdN RROZO R^RcR RSRTW RTJXJVY RYRXV R[H\\U][ R`C`L R^P^QcPcR RVDZH RjEkH RVCTH R[HeHdJ RYXRZ R]TdTd[b[ RhChPfZ R^WbV`[ RhKkZ R[QZ[ RcI]J',
	    'Po]HcH RTJ[J RRO[O R^OjO R^QjQ R^SlS R^VjV RTQTV RWJWY RZQZV R_L_S ReKeS ReDkDkJ RWC[G RVFRJ RWCTH R_J]O R]D]JaJ R`WeYl[ ReFiF RaF]F R`FcF_F RkFgF R]DcDcJ_J ReDeJgIkIgJ R[XRY RiWdY\\[',
	    'PoTJZJ R_LiL RRO[O R^RjR R]TkT R\\WlW RTRTV RWJWY R]F]J R^N^R R_I_L RbXb[ RdCdG RdNdW R_IiIiL R^NjNjR R]FlFlJ RiXl[ RWD[H R_D`F RiChF R^Y\\[ RVERJ RfXf[ RZRYV R[XRY',
	    'Po]GlG R_KcK RhKlK RTJZJ RSOZO R_PlP R`WhW R^ZiZ RTQTV RWJWY RYQYV R_M_P R`S`W RdRdY ReHeL RgMgP R`ThThW R_MlMlP R]DlDlG RWC[G RcMcP]U RVCSJ R`SlRl[i[ RiIlI R`IcI RbI_I RjIgI RZXSY R]D]R[Z',
	    'Po[ElE RTJYJ R\\KbK ReKkK R\\NbN ReNkN RROZO R_PiP R_VhV Rh[k[ RWJWY RZQYV R\\I\\[ R_R_V R`C`G R\\IbIbN RdOdZ ReIeN RgCgG R_RhRhV ReIkIk[ RdUhY RVCZG RcU_Y RTQTV RVCSJ RZXRY RaSaV RfRfV',
	    'PoUGdG RTKaK RRQcQ RZKZ[ RXDRJ RbYXZ',
	    'PoTG^G RTL]L RRQ^Q Rb[k[ RXLX[ RcDcZ RVCSI R^XWZ RlUk[',
	    'PoTH\\H RTL[L RRQ\\Q R`[k[ RXLXZ RVCSI RdN_Y ReLcR RgJeO R_FjFgK R\\XV[ RlUk[',
	    'PoTG\\G RTL[L R^MlM RRQ\\Q RXLXZ ReCe[ RVCSI R\\WVZ',
	    'Po_FlF RTH\\H RTL\\L RRQ]Q Rb[g[ RXLXZ RgFg[ RVCSI R]XW[',
	    'PoTH_H RTL^L RRQ_Q Rf[l[ RYLYZ ReFeT RlCl[ RVCTI R_WX[',
	    'PoTH^H RTL]L RRQ^Q RXLXZ RcCc[ RhOkP RcLhN RVCSI R^WWZ',
	    'PoTH]H RTL\\L RRQ^Q Ra[e[ RXLXZ RfLf[ R_FlFeL RVCSI R]XWZ',
	    'PoTH\\H RTL[L R^MlM RRQ\\Q R]ZlZ RWLWZ ReDeZ RVCSI R\\XV[',
	    'PoTG\\G RTL[L R]NlN RRQ\\Q RWLWZ ReEe[ RVCSI R[XVZ RjD^F',
	    'PoTG\\G RTK[K RRQ\\Q RXKXZ RfEfX RlCl[ RVCTI R`D`T\\[ R\\WVZ R`N_X',
	    'PoTG]G RTL\\L RRQ]Q RXLXZ RVCSI RiDfF`I RjKgN`Q R\\XVZ RlRhW^[',
	    'PoTG\\G RTK[K RRQ\\Q RWKWZ ReTk[ RaEaMfW RVCSI R[XVZ ReMkMhUcY`[ R^EhEeM RaI_W',
	    'PoTH\\H RTL[L RRQ\\Q Rh[l[ RWLWZ R_EhEiZ RaLeP RUCSJ R\\WWY RlWlZ R_E_Q^X',
	    'PoTG]G RTL\\L RRQ^Q Re[j[ RXLXZ RbNgS RVCSI RcC^M R]WVZ RaIlHlTj[',
	    'PoTG[G RTL[L RRQ\\Q Rg[l[ RWLWZ R^H^[ RdDlDl[ R`CbH RUCRJ R[WVZ',
	    'PoTG\\G R]JlJ RTL[L RSQ\\Q RWLWZ RgWkZ RhOeW RVCSI R\\XVZ R_RfUbY][ RcD_R RiJgT',
	    'PoTH\\H RTK[K RRQ[Q Re[i[ RWKWZ RbQ^U ReU_[ RhLdV RUCRJ RdLaS R\\WVZ R^EiEcI^LlLjZ',
	    'PoTH\\H RTL[L RRQ\\Q RWLWZ RdTk[ R`JeU RdIeM RVCSI R\\XVZ RjJfT`Y\\[ R_FbQ R^FkFhQ',
	    'Po^ElE RTG\\G RTL[L R^OlO RRQ\\Q RWLWZ RiEi[ RVCSJ R[XVZ RaEaQ`W',
	    'Po^ElE RTG\\G ReKkK RTL[L RRQ\\Q Rf[j[ RWLW[ ReEeQ RVCSI R\\XVZ R_I_QlQj[',
	    'Po^ElE RTH[H RTL[L RRQ\\Q RWLWZ ReHe[ RgLkP RcM^R RVCSI RfEbO R\\XVZ ReFeM',
	    'PoTH[H R\\KlK RTL[L RRQ\\Q RWLWZ RhWl[ RbWeZ R`V\\[ ReOiY RVCSJ RcP_X R\\WVZ RdCdLgV RdKaV RdHbT',
	    'Po_ElE RTH\\H RTL[L RRQ]Q R_SjS R_ZlZ RXLX[ R_E_Z R_KjKjS RVCSI R\\XV[',
	    'PoTH\\H R^GlG RTL\\L RSQ\\Q R_SkS Re[l[ RWLWZ R_K_S ReCe[ RkKkV RlXl[ RVCSI R\\XVZ',
	    'PoTG\\G RTL\\L RRQ\\Q RWLWZ RdCdS RiHlN RVCSI R\\XVZ RjReX][ R`H^P',
	    'PoTG\\G RTL\\L RRQ]Q R_RlR RXLXZ R_I_T ReCe[ R_IlIlT RVCSI R\\XVZ',
	    'PoTG\\G RSL\\L RRQ\\Q Rh[l[ RXLXZ R^J^[ R^JlJl[ ReCeMiU RVCSI ReMaV R\\XVZ ReGdR',
	    'PoTG\\G RTK[K RSQ\\Q RXKXZ R_D_T R_DjDjT RhWk[ ReVhW RVCRK R\\XVZ RcV`Y[[ RdHdOcV',
	    'PoTH\\H RTL\\L RRQ\\Q Rh[l[ RWLWZ R^E^[ R^ElEl[ RcKeOiV RhIaX RVCSI R\\XWZ',
	    'PoTG[G RTLZL R_LkL RRQ[Q RWLWZ RdUjZ RbNfW RVCSJ R[WVY RjMgUcY_[ RbLdU R_E_S]Z RjD`E',
	    'PoTH[H RTL[L RRQ\\Q RWLW[ RcKfN RdElM RcG]M R^QiQd[ RUCRJ RdCaI R\\XV[',
	    'PoTH]H RaKlK RTL]L RRQ]Q RaRlR Rh[l[ RWLWZ RaElEl[ RVCTI R\\WVZ RaEaR`X',
	    'PoTG]G RTK\\K RSQ]Q RXKXZ RgVl[ RbV][ RdPhX RaHlHiM RVCSI RdP`Y R\\WVZ RbD_M RdKcU',
	    'PoTH\\H RTL[L RRQ\\Q Rd[j[ RWLWZ RcLfP RVCSI RbC_L R\\WVY RiR_W RaHlHlTj[',
	    'PoTH\\H RTL\\L ReMjM RSQ\\Q R^UiU Rf[k[ RWLWZ RdCcF RVCSI R\\XVZ R`FjFjM R`F`PlPlVk[',
	    'PoTG\\G RTK[K RRQ\\Q Re[j[ RWKWZ RVCSI RbC^L RcK_U R]WWZ RfOhT_T R`HlHlSj[',
	    'PoTG[G R]HlH RTK[K RSQ[Q Rg[l[ RWKWZ R_MgMhZ R[XVY RbCcG RVCSI R\\ZZ[ RlVl[ R_M_S^X',
	    'PoTH[H R]HlH RTLZL RRQ[Q Rd[i[ RWLWZ R_W\\[ RdCeG RVCSJ R[WVY RbHbPjOjUi[ RbK`W',
	    'PoTG\\G RTL[L RRQ\\Q RWLWZ RfVk[ RbV^Z RkIhO RVCSJ R\\XVZ ReCdPgW R`I^P RdLeU RdKcV',
	    'PoTH\\H RTL\\L RRQ]Q RXLXZ RhCh[ R_MdN RbFeH RVCSI R\\XWZ RlS^U',
	    'Po]EkE RUH\\H RTL[L RRQ\\Q R\\[l[ RWLWZ RkGkP RVCRK R[XV[ R]OjOjUi[ RdEcLbQa[',
	    'PoUH\\H RTL[L R_PkP RSQ\\Q R_[k[ RWLW[ R_E_Z RdEdP R_EkEkQ RlWk[ RVCRK R\\XVZ',
	    'Po^ElE RTH[H RTL[L R_OkO RRQ\\Q R]ZlZ RWLW[ ReEeZ RhRjW RVCSJ R\\XVZ',
	    'PoTH[H RTL[L RSQ\\Q RWLWZ RlXl[ Rh[k[ RfDjF RVCSI R\\XVZ RkQfV]Z RcCcLeW RgI^J RiI`J RkM]P',
	    'Po^FlF RTH\\H RTL[L ReOkO RRQ\\Q R]ZlZ RWLW[ R_L_Z ReFeZ RVCSJ R\\XV[',
	    'PoTG\\G R]IlI RTL[L R`QiQ RRQ\\Q R`YiY RWLWZ R`C`[ RiCi[ RVCSI R\\WVZ',
	    'PoTG\\G R^JlJ RTL[L RRQ\\Q R_ZkZ RWLWZ R_Q_[ ReCeQ R_QkQk[ RVCSI R\\WVZ',
	    'PoTG[G R]IkI RTL[L RRQ\\Q R_ViV RWLWZ RdCd[ RhRlU R_R\\U RdHiT RVCRK RdH^T R[XVZ',
	    'PoTG\\G RTL[L RRQ[Q R_SeS Re[j[ RWLWZ R_K_U R_KeKeS R^EkEj[ RVCSJ R[XVZ',
	    'Po_ElE RTG\\G RTL[L RRQ\\Q RdTjT R_ZlZ RWLW[ R_E_Z RdKdT RdKjKjT RVCSJ R\\XV[',
	    'PoTG[G R]HlH RTL[L RSQ\\Q RhWkW RWLWZ R`M`W ReJe[ R_NkNkW RaK\\P RVCRK RdD_N R[XVZ',
	    'PoTH\\H R]IlI RTL\\L RRQ\\Q RWLWZ RdTjZ RgDjG RhTb[ RbCbNfW RaNjNfW RVCSI R`R]Z R\\XVZ RbH_V',
	    'PoTG[G R^IlI RTK[K RRQ[Q RWKWZ R^I^Y RhDkG RgUb[ RjOfX RVCSI R[XVZ RcW]Y RjLgW RfCfLgX RlWk[iZ',
	    'PoTG]G RdIlI RTK[K RRQ\\Q R_ZkZ RWKWZ R_Q_[ RdCdQ R_QkQk[ RVCSI R\\XVZ',
	    'PoTH\\H R`KlK RTL[L RRQ]Q R`RlR R`YlY RXLXZ R`E`[ R`ElEl[ RVCTI R\\XV[',
	    'PoTH\\H RTL[L R_MjM RRQ\\Q R_UjU R^[l[ RXLX[ R_E_U R_EjEjU RVCSJ R[XVZ',
	    'PoTH\\H R_KlK RTL\\L RSQ\\Q R_RlR RXLXZ R_D_T RfDf[ R_DlDlT RVCTI R\\XVZ',
	    'PoTH\\H RTL\\L R_OlO RRQ\\Q R_YlY RWLWZ R_E_[ ReEeY R_ElEl[ RVCSJ R\\XVZ',
	    'PoTH\\H RTL\\L R_QlQ RRQ\\Q R_YlY RXLXZ R_I_[ ReDeY R_IlIl[ RVCSJ R\\XVZ',
	    'PoTG\\G R_IkI RTL\\L R^PlP RRQ\\Q RXLXZ RgVl[ RcU^[ ReCeQiY RePaX RVCSI R\\XVZ R`E^M ReNfU ReLdU',
	    'PoTG]G RTK\\K R_QlQ RSQ]Q R_ZlZ RXKXZ R_H_[ R_HlHl[ RVCRJ ReCcH R\\WVZ',
	    'PoTG\\G RTL[L RRQ\\Q RWLWZ RcWg[ RcKeM ReFlM RgUdX RcG]M ReDfH R_PiPfW RVCSI RdCbH R\\XVZ',
	    'Po_EfE RTG[G RTL[L R]NlN RSQ\\Q Ra[d[ RWKWZ ReHe[ RiRlY RVCRK R`R]Y R[XVZ R_F]N RjDaE',
	    'PoTG\\G RTK\\K RhNlN RRQ\\Q R_YjY RXKXZ R_P_[ RaEhEiM R_PjPj[ RVCSI R\\XVZ RaE`M',
	    'PoTG[G RTLZL RSQ[Q RhVlV RWLWZ R^F^U RgFg[ RgFlFlU RbW][ RVCRJ R[XUZ RdD^E RcGcT]U RcNbW',
	    'PoTH[H RTL[L RRQ[Q R_SfS RfUiU R_[k[ RWLW[ R_K_[ R_KfKfS R_GjGjT RlXk[ RVCSJ RaC]K R[XVZ',
	    'PoTG[G R]GlG RTL[L RRQ[Q RhWkW RWLWZ R^M^W RdGd[ R^MkMkW RcCdF RUCRJ R[XUZ',
	    'PoTG\\G R]HlH RTL[L R_QfQ RSQ\\Q RcYkY R^ZfZ RWLWZ RdCeG RiMfP ReR^Y RiVk[ RVCSI RdH`P R\\XV[',
	    'PoTH[H RTL[L RRQ\\Q Ra[k[ RWLWZ R]H]K RaLa[ R]HlHlK RlWk[ RdCeF RVCSJ RiN`S R\\XVZ',
	    'PoTG\\G RTL[L RRQ\\Q Rb[j[ RWLWZ RcIc[ RjWj[ RdS\\[ RaDeF RjFdT RUCRJ R[XVZ RjLlT R^M]T',
	    'PoTG[G R^KjK RTL[L RRQ[Q Rb[k[ RWLWZ RcMcZ R^EjEjJ RlWk[ RVCSJ RjObS R[XUZ R^E^R\\Z',
	    'PoTG\\G RTL[L RRQ[Q RWLWZ RdCdN R^HlHjL R`NbTgYl[ RVCSI R[XVZ R^NjNgTcY_[ R^H^R\\Z',
	    'PoTG[G RTK[K RRQ[Q R^RjR R\\VlV RWKWZ RdOd[ R_FdKkO RVCSJ R]EjEeK]O R[XVZ',
	    'PoTG[G RTK[K R]NfN RSQ[Q Rh[l[ RWKWZ R]EeEd[ RhEhT RlCl[ RVCSJ R[XVZ R_E_Q^X',
	    'PoTG[G R]GhG RTKZK R\\LlL R`PlP RRQ[Q Rb[h[ RWKWZ RcCcL RVCSI RbP\\R R\\XVZ RjFaO`UiUh[',
	    'PoTH[H R^GhG RTL[L R]MlM RRQ[Q Rb[k[ RWLWZ RbPb[ RcCcM RhJ^T RlXk[ RkDhJ RUCRJ R[XUZ RjRbU',
	    'Po^ElE RTG\\G R`KiK RTK[K R`QiQ RSQ\\Q RXKXZ R`E`W RiEi[ RVCSI R\\WWY RiV]X RlVaW',
	    'PoTH\\H R]GlG RTL[L R]OlO RRQ\\Q R`ZkZ RWLWZ R`O`Z RaCaJ RhChJ RcKfN RVDRK R[XVZ',
	    'Po\\EkE RTH[H RTLZL RRQZQ R[ZlZ RVLV[ RbEbZ RfEfZ RUCRJ RZXUZ R]K^T RkKiT',
	    'PoTG\\G R^HlH RTL[L R`PjP RRQ\\Q R`UjU Rf[j[ RWLWZ R`K`[ R_LjLj[ RVCSI RbE^N R[XVZ',
	    'PoUH[H R^HlH RTL[L RSR\\R R`WcW RWLWZ R^NdNdV RlWk[ RiDkF RhVcZ RkNfX RVCSJ R[XVZ RgCgLgOhY R^H^S\\[',
	    'PoTG[G R^GkG RTKZK R]QlQ RSQ[Q RWKWZ RfVk[ RcU][ R_J`N RdCdQgX RdQaX RiJgN RVCSI R[XVZ RdLcU',
	    'PoTGZG RTLZL R\\NdN RRQ[Q RWLWZ RfEf[ RUCSJ R`O[Y RYXUZ RkWhW R\\EcEa[^[ RfElEjMkQlU R]G\\N',
	    'PodGjG RTGZG RaGhG RSKZK RRQZQ R\\RlR Rf[l[ RWKWZ RgRg[ RkLkP RlXl[ R`W][ R^HbGcLfNjO RUCRJ RhJ_N RZXUZ RaCcK RaR`Y',
	    'Po]ElE RTH\\H RTL[L RcMkM R_MgM RRQ[Q R^SkS R\\[l[ RWLWZ RdOd[ RgHkL RVCRJ RbH`M RcEaJ R[XUZ',
	    'PoTH\\H RTL\\L RRQ\\Q R_SlS R^YlY RXLX[ ReCeM R^NlNl[ R_FaK RkEjK RVCSI R\\XV[',
	    'PoTH\\H RbIiI RTL\\L RSQ\\Q RbThT Rh[l[ RWLWZ R^E^[ RbMbV RbMhMhT R^ElEl[ RVCSI R[XVZ',
	    'PoTH[H RTL[L R_MkM RRQ\\Q R_ZlZ RWLWZ R^Q^[ R_E_L R_EkEkL R^QlQl[ RVCTI R\\XVZ',
	    'PoTH\\H R`KkK RTK\\K RRQ\\Q RWKWZ R_P_Y R`E`K ReKe[ R`EkEkK RVCTI R\\XVZ R_PlPlXiX',
	    'PoTG[G RTK[K R`LjL RRQ\\Q R^ZlZ RWKWZ R^E^[ R^ElEl[ ReGeOiW RVCSI R[XVZ ReJdS',
	    'PoTH[H R^JkJ RTL[L RRQ\\Q R^TkT R_[k[ RWLW[ R^D^J R_T_[ ReCeJ R^NkNkT RkDkJ RlXk[ RVCSJ R[XVZ',
	    'PoTHZH RSLYL RSQZQ Rh[l[ RWLWZ R\\E\\U R\\EdEdU RhFhU RlCl[ RaWdZ R_V[[ RVCSJ RZXUZ R`H`R_W',
	    'PoTG\\G R_IjI RTL[L R]PlP RRQ\\Q RWLWZ RdCd[ RgTlY RdNhU RVCSI RdNaU\\YVZ R`E^M',
	    'PoTG[G R`IkI RTL[L R]OlO RRQ\\Q Rg[l[ RWLWZ ReCeO RgOg[ RlXl[ RUCSJ RaD^L R[XVZ R_Z][ RbO`Y',
	    'PoTG[G RTLZL RcMkM RRQZQ RbVlV Rc[l[ RWLWY RgEgV R^VZ[ RVCRK R[WUY R[E`E\\MaM_W RkDbE R\\Q^VbYjZ',
	    'PoTH\\H R^JkJ RTL[L R^PlP RRQ\\Q RWLWZ RVCSI ReEdP_Y ReOaW R\\WWZ RjD^F RhUiWjZ^[',
	    'PoTG\\G RTL[L R^LlL RRQ\\Q R_YjY RWLWZ R_R_[ ReEeR R_RjRj[ RVCSJ R[XVZ RjD^F',
	    'PoTG[G RTKZK RRQ[Q R\\TlT RWKWZ R`F`P RdQd[ RfCfOlNlO RVCSI RaC^J RjEfJaL R[XUZ',
	    'PoTG[G RTL[L R_MiM RSQ[Q R_SjS R][l[ RWLWZ RdMd[ ReFlL ReCfG RcG]M RVCSI RdCbH R[XVZ',
	    'PoTG[G RTLZL R]OlO RRQ[Q Ra[d[ RWLWZ ReLe[ RjTlX R^EkK RVCSJ R`S^X RjCfH]K R[XUZ',
	    'PoTG[G RTK[K R`LhL RSQ\\Q R_ZiZ RWKWZ R_Q_[ R_QiQi[ ReFlM RdCfH RbG\\M RVCSI RdCaI R[XVZ',
	    'PoTG[G RTLZL RRQ[Q Rf[l[ RWLWZ RgCg[ RlWl[ RhQkT R`R\\S RbO_R RaV\\[ R]G_L RjGiL RVCSI R[XUZ RbCbQaW',
	    'Po`FjF RTG[G RTL[L RRQ[Q R_ZjZ RWLW[ R_R_[ R_RjRj[ R`G\\K R`IeNlQ RVCSJ RaC^I RjGdM\\Q R[XVZ',
	    'PoTG\\G RTL[L RRQ]Q RaZlZ RXLXZ RbQb[ R`RlRl[ RcF^K RVCTI RdCaI RcKfO^R RbGlGjJdQ^R R\\XVZ',
	    'PoaFhF RTG[G RTLZL R]OlO RRQ[Q R^TjT R`[d[ RWLWZ RdJd[ R^KjJjU RgGdJ RbF]K RVCSJ RbC`H R[XUZ',
	    'PoTH\\H RTL[L RRQ\\Q R_RkR R_[k[ RWLWZ R_K_[ ReKeR R_KkKkR RlWk[ RVCSJ RbC^J RaGhFeL R\\XV[',
	    'PoTG[G R^GlG RTL[L RSQ[Q RXLXZ RhJlN RdCeF RaK^O RaOdUiYl[ RVCSI R[XVZ RhOfUaY][',
	    'PoTH[H R]HkH RTLZL RRQ[Q RWLWZ RbMb[ RhVlZ RcL]R RkMgR ReNjX RdDeG RUCRJ RdHaO R\\XUZ RdJgU RfY`Z',
	    'Po^GlG RTG[G RTLZL R^LlL RRQ[Q RWLWZ ReDeG RaHbK RhHgL RUCRJ R[XVZ R^L^S]Z',
	    'PoTH[H R^HlH RTL[L RbOjO R^OgO RRQ[Q Rf[l[ RWLWZ RgPg[ RlXl[ RgKkN R`W][ RdHbK^O RdCeF RUCRJ R\\XVZ RaQ`X',
	    'PoTHZH RTLYL RRQZQ Rf[j[ RVLVZ R\\K_N R\\D_G RcR_V ReVa[ RhOdY RUCSJ ReMaU RZXUZ RiMfV R^R[Z RaEjEaMlMlRj[',
	    'PoTH[H RTLZL R]NlN RRQ[Q RWLWZ RdCeG R_TjY RUCRJ RdI_S R[XVZ RiOgUdX][ R^G^K R^GlGlK',
	    'PoTG\\G R^JjJ RTL[L R^OjO RRQ\\Q RWLWZ R^E^[ R^EjEjO ReUjZ RcOfW RVCSJ R\\XVZ RkReU RdY]Z',
	    'PoTGZG RTLZL RRQZQ RfYlY RWLWZ RfFf[ RfFlFl[ R^RdX RaU\\[ RVCSJ RcP_Y RZXVY R`C^R R\\IdIaV',
	    'PoTG[G R\\GkG R]KjK RSLZL R[NlN RRQ[Q R`SlS Re[i[ RWLWZ RiPi[ RbUeX RYWV[ R_UZY RUCSJ RbN]W RcH_U RdC`R',
	    'Po\\FlF RTGZG RSLZL RRQ[Q Rd[h[ RWLWZ R]L]O RaCaI RhChI R]LlLlO RbV]Z RUCRJ R[WUZ RcOaW R]RjRh[',
	    'PoTG[G R]GlG RTK[K R_PkP RRQ\\Q R_UkU Rh[k[ RWKWZ R_K_[ ReCe[ R_KkKk[ RhDkE RVCSI R[XVZ',
	    'Po]DkD RTH[H RTL[L R\\OlO RRQ\\Q R^ZjZ RWLWZ R^R^[ R]IiIiO R^RjRj[ RVCSI R\\WVZ RbE`O',
	    'Po^GkG RTG[G RTLZL R]PlP RRQ[Q RWLWZ RdCd[ ReRlY RcPgS R_IaM RiIgM RUCSJ RdNbT[XVZ',
	    'PoTGZG R\\IlI RSLYL RRQZQ R_YdY RVLVZ R`P`Y RdVdY RhDkF RdQfV R_LbM RUCRJ RZXUZ R\\Q[V RlVk[ RgCgMiZ',
	    'Po_GlG RTHZH RSLYL R`MkM RRRZR R`SkS Rb[l[ RVLVZ RfJfY R\\E]H R^N^XZ[ R^Xc[ RUDRJ ReC`M RYYUZ RZN^N',
	    'PoTHZH RSLYL RRQZQ R]TkT R[[l[ RVLV[ R]E]O RaCaP RdQd[ RfKkP RiKdP RdFhM RcEkEgM RVCSJ RZXVZ',
	    'PoTG\\G RTL[L R^PkP RRQ\\Q R^UkU Rg[k[ RWLWZ R^K^[ ReCeK R^KkKk[ RkFhI R_EaI RUCSJ R[XVZ',
	    'PoTG\\G RTL[L RSQ\\Q RWLWZ R_K_W RdCdK R_KjKjW R_DaI ReXl[ RVCSI RjEhI R\\XVZ RdNdVaY\\[',
	    'PoTH\\H R_JkJ RTL[L R_PkP RRQ\\Q R_UkU R][l[ RWLW[ R_D_P ReDe[ R_DkDkP RVCSI R\\XV[',
	    'PoTH[H R_LjL RTL[L R]OkO RSQ[Q R^UkU R\\[l[ RWLWZ R_D_K RdOd[ R_DjDjK RUDRJ R[XV[',
	    'PoTGZG R\\KbK RTLZL R\\QbQ RRQZQ Rf[j[ RWLWY R\\E\\Y RgCgRc[ RUCSJ RZXVY R\\EbEcV[X RdJlJlUk[ RgLfW',
	    'PoTG\\G R_JkJ RTK[K RRQ[Q Rg[l[ RWKWZ R^O^[ R_D_J R_DkDkJ R^OlOl[ ReJeQjV RdS`V RVCSI R[XVZ ReLdS',
	    'PoTG\\G R_IkI RTK[K R]NlN RRQ\\Q R_ZjZ RWKWZ R_R_[ ReCeN R_RjRk[ RVCSJ R\\XVZ R_D^K',
	    'PoTH[H RTLZL R\\LlL RRQ[Q R][`[ RWLWZ R`E`[ RiEjI RhVc[ RjOfX RlWk[ RVCSI R[XVZ RcD\\E RdQ\\S RfCfNhZ',
	    'PoTG[G R]IlI RTK[K RRQ\\Q Re[j[ RWKWZ R^EeEeO RdHkO RdH^O RUCSI R[XVZ R^Z\\[ R^QgQfTkTj[ RaQ`X RiD`E',
	    'PoTG[G RTL[L RRQ[Q R]TjT R\\[l[ RWLWZ RdCd[ R_JbP RhJjMlP RVCSJ R[XV[ R`F^P RiFgO',
	    'Po]EdE RTH[H RTLZL R]PlP RRQ[Q Rd[h[ RWLWZ RhMh[ R_ScW R^G`L RdGeK RjFhK RVCSI R[XVZ RjDcD',
	    'PoTHZH RTLYL R^QjQ RRQ[Q R^TjT R]WkW RWLWZ RdNd[ RaE\\J R`GdLlO RUCSJ R`GiGcL\\O R[WVY',
	    'Po^GkG RTG\\G RTL[L R]NlN RRQ\\Q R^TlT RWLWZ ReNe[ RdCeF RaHaM RiHfN RUCSJ R[XVZ',
	    'PoTG[G R]GlG RTL[L RaMjM R^MhM RRQ\\Q Rh[l[ RWLWZ RdPdZ RiPi[ RlXl[ RgJlN RcH]M RcCeG RUCSJ R[WVY R]Z[[ R_Q^X',
	    'PoTG[G RTKZK R`PjP RRQ[Q Ri[l[ RWKWZ R]H][ RaIhIgY RbElEl[ R^D`F RVCSI R[XVZ RcIcQbX',
	    'PoTG[G RTLZL RaPhP RRQ[Q RaVhV Rh[l[ RWLWZ R]I][ RaJaV RaJhJhV RcElEl[ R]D`G RVCTI R[XUZ',
	    'PoTG[G RTK[K R^PjP RRQ[Q Rf[l[ RWKWZ R^I^P RgPg[ R^IjIjP RlWl[ R`W\\[ R`CaG RUCSJ RiCfI R[XVZ RbP`Y',
	    'PoTG[G RTK[K R^MkM RRQ[Q RhXkX RWKWZ RdHd[ R]HkHkM R`CaG RVCSI RhCgH RdR\\YVZ R^M]RlRkX',
	    'PoTG\\G R_KjK RTK[K R_PjP RRQ\\Q RWKWZ R_G_[ R_GjGjP ReTjZ RcPfW RcCdF RUCRJ R[XVZ RjSfU ReY^Z',
	    'PoTG\\G R_HjH RTL[L R^LjL RRQ[Q R_RjR RWLWZ R]O]R R_DjDjK R]OlOlR RaTeXk[ RVCSJ R[XVZ RiSeX^[',
	    'PoTH[H R^JkJ RTKZK RRQ[Q RaWhW Rf[k[ RWKWZ RaQaX RaQhQhV R^DkDkJ RVCSI R[XUZ R^NlNlVk[ R^D^R\\Y',
	    'PoTHYH RRQYQ RcSgS Rf[j[ RSLWLVZ R[D[[ RcJcU RcJgJgS RbEkEj[ RYXUY R^LaR RUCRJ R[E`D^M R`U]U',
	    'Po]FkF RTG[G R^IkI RTL[L R]LlL RSQ[Q R^SjS R^VjV Rf[j[ RWLWZ R^O^[ RdCdL R^OjOj[ RVCSI R[XVZ',
	    'PoTH[H R^GhG RTL[L R\\LlL RRQ[Q R`UjU R`ZjZ RWLWZ RaPa[ R_QjQj[ RjGbO RVCSI RcCdLaP\\R R[XVZ',
	    'Po]FlF RTG[G RTLZL R]MlM RRQ[Q R`ZjZ RWLWZ R`R`[ RaCaJ RhChJ R_SjSk[ RaQ\\U RVCSI RcJ_T R[XUZ',
	    'PoTG[G R^GkG RTL[L R]MlM RRQ\\Q R_UjU R_ZjZ RWLWZ R_P_[ RaCaM RhChM R_PjPj[ RUCSJ R[XVZ',
	    'PoTG[G R^GlG RTKZK RRQ[Q R^TkT R^ZkZ RWKWZ R^N^[ RbCbK ReNeZ RhChK R^NkNk[ RVCSI R\\XVZ',
	    'PoTG[G R]GlG RTLZL R_MiM RRQ[Q R\\TlT RWLWZ RdJdQ RhPh[ RfHlM RcG\\M RVCSJ RdCaJ R[XUZ R`R_Y',
	    'Po]GlG RTG[G RTL[L RSQ[Q R_WeW Rf[j[ RWLWZ R_R_Y R_ReReW R\\NkNj[ RdIlL RUCRJ RdHaK]M R[XVZ RdCcJ',
	    'PoTG\\G R_HjH RTL[L R_LjL R^OlO RSQ\\Q R]SlS Rd[h[ RWLWZ R_D_L RhOh[ R_DjDjL RaUcX RVCSI R\\XVZ',
	    'PoTG[G R^IjI RTL[L R^NjN RRQ[Q R]RlR RWLWZ R^D^N RdDd[ R^DjDjN RfSlY RcS]Y RVCSJ R\\XVZ',
	    'PoTG[G R^IjI RTK[K R^MjM RRQ\\Q R_SdS Rf[l[ RWKW[ R^D^M R_O_[ RfOf[ R^DjDjM RlXl[ RVCSI R\\XVZ RkRfT RdZ]Z',
	    'PoTG\\G R`IkI RTK[K R`MkM RRQ\\Q R_UlU R_ZlZ RXKXZ R_P_[ R`D`M R`DkDkM R_PlPl[ RVCSI R\\WVZ',
	    'PoTG\\G R_IjI RTL[L R_MjM RRQ\\Q Rg[j[ RWLWZ R_D_M R_DjDkM RaO[U RcT]Y RfVa[ RhQeY RVCSI RcQaV R\\XVZ RaM_RlQj[',
	    'PoTG\\G RaJjJ RTL[L RRQ\\Q RbViV R^ZlZ RWLWZ R^D^[ RbObV ReGeO RbOiOiV R^DlDl[ RVCSI R\\WVZ',
	    'PoTG\\G R^KlK RTK[K RRQ\\Q RXKXZ R^D^K RgDgK R^DlDlK RcDcLbO\\T RjRdX RaPkOhT RUCRJ R\\WVZ RbSfW\\[',
	    'PoTH\\H R\\JkJ RTL[L R\\OlO RRQ[Q R\\UlU R^[j[ RWLWZ R_J_U R]FdEd[ RiJiU RVCSJ R[XU[ RiD`E',
	    'PoTG\\G R_IlI RTL[L R_NkN RRQ[Q R_SkS R_YlY RWLWZ R`H`[ RfHfY ReDfG RVCSI RaD]L R\\WVZ',
	    'PoTH\\H R_JlJ RTL\\L R_NlN RSQ\\Q RiYlY RXLXZ R_R_Z R_F_N RdCcF ReNe[ R_FlFlN R_RlRlY RVCSJ R\\XVZ',
	    'Po^FdF RTG[G R^JlJ RTL[L RRQ[Q RWLWY RaNaV ReGeM RaNjNjV RiXk[ RgWjX RUCSJ R[WVY RdY_[ R^F^S\\Z RePeX RjDaE',
	    'PoTGYG RTLYL RRQZQ R\\KaK`[ RhSk[ RUCSJ RVLWYUZ RgOjY ReHkHiN RfDdN RgLiW RbD\\F\\TZ[ RgMeX',
	    'PoTHZH R]IjI RTLZL R\\NkN RRQZQ RdWgW R_[i[ RWLWZ R`R`[ R_RgRgW R]D`G RfNlS RiDfG RjXj[ RaN[S RVCSI RcH^Q R[XUZ RcC`O',
	    'Po^GkG RTHZH RTLZL RRQ[Q ReQiQ R_[h[ RWLWZ R`S`[ RbSeV RYYUZ RhXh[ R`K[P RjTlY RUCSJ R_C\\J RbF_M RgHcP R\\T[Z RkHjQ',
	    'PoTG[G R]GkG RTLZL R\\NlN RRQ[Q R_ZjZ RWLWZ R_S_[ R_SjSj[ RdCeG RiHgN RUCRJ R[XUZ R`H`M',
	    'PoTG[G RTL[L RSQ[Q RWLWZ RgLkM RcJ_N RiFgI RbV^[ RjRfU RcReWhYl[ RcIhL RVCSI R_E]I R_Q]V R[XVZ RcObX RdCcK',
	    'PoTG[G RTL[L R^LkL RRQ[Q RdRkR Rd[l[ RWLWZ R]G]J ReLeZ R]GlGlJ R`WcZ R_TaY RdCdF RVCSI R[XVZ R_Q]Y',
	    'PoTGZG RaIlI RTLYL RbMjM RbQkQ RRQZQ RaUlU Rd[l[ RVLVZ RfCfY RbFjFjM R]WZ[ RVCRK R_R\\Y RZXUZ R[E`E\\M`M^W R\\Q^VbZj[',
	    'PoTH[H R^JkJ RTLZL R^NlN RRQ[Q R`ZkZ RWLWZ R^E_T`[ ReJeS R`TkTk[ R^EkEkJ RVCSI R[XUZ R^N]Y',
	    'Po^EiE RTH[H R\\JkJ RTL[L R`PdP RRQ[Q R[[l[ RWLWZ R^S^[ RbSb[ RdHdP RfSf[ R^SjSj[ RUCRJ RhFdG R[XVZ',
	    'PoTG\\G RTL[L RRQ\\Q R^UlU R^ZlZ RWLWZ R^P^[ RePeZ R^PlPl[ R_I`M RdJfM RhIlL RkDiG ReDdHdK RVCSI R`D^H R[XVZ',
	    'Po\\FcF RTGZG R\\IcI RSKZK R\\McM RRQ[Q R\\TlT RWKWZ R_C_P ReEkEkN RdSeVhYl[ RUCSJ R[XUZ RdSbW`Y\\[ RgEeN RjOfO',
	    'Po\\FlF RTG[G RTK[K RRQ[RlS RWKWZ R^L^S RaCaI RfCfI R^LiLiS RdIdSeWhYl[ RVCSJ R[XVZ RcU`Y[[ RdNcV',
	    'PoTG\\G R^HcH RTK[K RRQ[Q R_VjV R_ZjZ RWKWZ R^C^N R_Q_[ RcOcQ R_QjQj[ RfCfNlLlN RVCSI RlFfI R[XVZ RcM]N',
	    'PoeFlF RTGZG RTLZL RbQlQ RRQ[Q Re[i[ RWLWZ R_C_[ RiMi[ RcTfW R_P[T RkHeM RfDbH RUCRJ ReIhKbN R[XUZ R\\H]M',
	    'PoTG\\G R^JjJ RTL[L R^OjO RRQ\\Q Ra[h[ RWLW[ R^E^O RbSb[ RdEdO RhXh[ R^EjEkO RcQfT RiSlY RVCSJ R[XVZ R^S]Y',
	    'PoTG[G R^JcJ RfJlJ RTKZK R_NjN RRQ[Q R]QlQ Rd[i[ RWKWZ R^E^J R^EcEcJ RfEfJ RfElElJ RVCSI R\\XVZ RbQ`UjUi[',
	    'PoTH[H R]JlJ RTKZK RRQ[Q R^TbT RfTkT R^ZkZ RWKWZ R^O^[ R^EeEeY RfOkOk[ RUCRJ RbN^N R[XUZ RjCaE',
	    'PoTGYG R[KbK RRQYQ RSKXKVZ R_D_[ RfSc[ RUCRJ RZXUZ RkIjP R_J\\U RgCgOeX RbD[E RdIcO RgJiX',
	    'PoTH[H R]HlH RTL[L R^OkO RRQ[Q R^SkS R^WkW R\\[l[ RWLWZ R^K^S R]EbEeEd[ R^KkKkS RVCSJ R[XV[ RjDbE',
	    'PoTGZG R]JbJ RTLZL RjLlL R]PbP RRQZQ RWLWZ R^E^[ ReDeK ReDjDjL RfUk[ RiUcZ RdPgX RcPkPgW RUCSJ RZXUZ RbD]E RbU[V',
	    'PoTGZG R\\JaJ RfJkJ RTKZK R\\NkN RRQZQ RVKVZ R]F]N RdCdR RfFkFkN RUCSJ R[XUZ RaE\\F R]RaWfYl[ R\\SjSgWbYZ[',
	    'PoTH[H R^JjJ RTL[L R^OjO RRQ[Q R]RkR R^VjV R\\[l[ RWLWZ R^F^N RdRd[ R^FjFjN RcCbF RVCSJ R[XVZ',
	    'PoTGZG R\\KlK RSKZK R[NlN RRQZQ R_RiR RVKVZ R\\EdEcI R]WY[ R^F^I R_SdXk[ RVCSI R_Q[Y RiFgJ RZWVY RiTdX^[ R`K^W RjCcD',
	    'Po\\GlG RTGZG RTKZK R^PiP RRQ[Q RWKWZ R^J^O RaRaZ RcCcG R^JiJjO RfUkZ RkRhU RdPgW RUCSJ RcP_S[U R[XUZ RdX_[',
	    'Po^FlF RTG[G R`KkK RTL[L RbOhO RRQ[Q R`RjR RWLWZ RbHbO RiHiO ReCfF RbTfYl[ RUCRJ R[XVZ RjSfX_[ R^F^R\\Z',
	    'PoTH[H R]GkG R^KjK RTL[L R\\OlO RRQ[Q R]TlT RWLWZ RdGdO R`CaF RhCgG RdReVhYl[ RVCSI R[XUZ RcV`Y[[ RdPbX',
	    'PoTG[G R\\IlI RTLZL RRQ[Q R\\SlS RWLWZ RdCdO R_D`H RjDhH RdHhLlN RVCSI RbP`U RdH`L\\N R[XUZ R_WkZ RhSgVdY][',
	    'PoTGZG R\\IlI RTLZL RSQ[Q R\\ZcZ RWLWZ R`DaG RgJeR RkMeZ RUCRJ R`J]Q RbN^X RhCfH RZXUZ RbUc[ RbQ]Q RjQdR RjUkZdZ',
	    'PoTG[G R^IkI RTLZL RRQ[Q RWLWZ R^Q^W RfCfO R^QjQjW R]FkFkI RbCbL]O RfXl[ RUCRJ R[XVZ RcX][ R^I]LlLkNiN RdSdX',
	    'PoTG[G R^JlJ RTKZK RRQ[Q RaRlR RaVlV RaZlZ RWKWZ RaMa[ ReDeJ RaMlMl[ R^DlDlJ RVCSI R[XUZ R^D^R]Y',
	    'Po]FlF RTGZG RTLZL R^MjM RRQ[Q R^QjQ R]UlU RWLWZ R^J^Q RaCaH RgCgH R^JjJjQ ReVhYl[ RVCSI R[XUZ RcV`Y\\[ RdQbX',
	    'Po]FlF RTG[G RTL[L R_LjL R_OjO RRQ[Q R_SjS R\\VlV RWLWZ R_I_V R_IjIjV RhXk[ RaY][ RVCSI R[XVZ RdCcI',
	    'Po\\FlF RTG[G RTL[L R^MkM R^PkP RRQ[Q R\\TlT Rc[g[ RWLWZ R^I^R RdCdQ RgRg[ R^IkIkQ R`VbY RgDjD RVCSI R[XUZ',
	    'Po\\ElE RTH[H RTL[L R^LjL RRQ[Q R_UiU Rh[l[ RWLWZ R]O][ R^H^L RdUd[ R^HjHjL R]OlOl[ R`QcS RhQfT RVCSI R[XVZ',
	    'PoTGZG R^HiH R^KiK RTKZK R\\OkO RRQZQ RWKWZ R^E^N R\\EjEiO RfUkZ R]UbY R[T^U RaU[[ RjUeZ RdQgX RcQkQhX RUCRJ R\\QbQ_X RZXUZ',
	    'PoTG[G RTK[K RRQ[Q Rf[l[ RWKWZ R]I]M RdCdI R`N`ShSg[ R`NiNiS R]IlIlM RlXl[ R_E`H RjEiH RbW^[ RVCSI RcT`Y R[WUZ',
	    'Po_GlG RTG[G R_JkJ RTK[K R_MkM R_PlP RRQ\\Q Rf[j[ RWKWZ R_F_Q RfGfP RdCfE RaW][ RVCSI R`C]J R[XVZ R]ShSgVkVk[ RaS`Y',
	    'PoTG[G R^IjI RTL[L R^LjL R^PjP RRQ[Q R\\SlS RWLWZ R^F^O RdPd[ R^FjFjO RdSkZ RdS]Z RcCcF RVCSI R[XVZ',
	    'PoTG[G R`HgH RTLZL RRQ[Q R\\TjT R\\WlW R`[c[ RWLWZ R]QdQc[ R]KiKiN RcCfFlI RVCSJ R[XUZ RcC_G[I R]K]NiOaQ',
	    'PoTG[G RTLZL RRQ[Q R^UkU R^ZkZ RWLWZ R^E^N R^P^[ RePeZ R^PkPk[ RVCSI RbHcK]M ReElElNhN R[XUZ RcD]E RgEfM',
	    'Po]FlF RTH[H RTL[L R_MiM RRQ[Q RaXgX RWLWZ R]P][ R_I_M RaSaX RaSgSgX R_IiIjM RcCdF RVCSJ R[XVZ R]PkPk[h[',
	    'Po]FkF RTG[G RSLZL R]PlP RRQ[Q Rd[i[ RWLWZ R]K]O RdCeF RdMeP R]KlKlO RaW][ R`H`J RiGgK RUCRJ RZXUZ RbQ`Y RaUjUi[',
	    'PoTH[H R\\JlJ RTLZL RSQZQ R[[l[ RWLW[ R^R^[ RbRb[ RfRf[ R^RjRj[ RgLkQ RaL\\Q RiDgH R`DaH RVCSJ R[XVZ',
	    'PoTGZG R^KiK RSLZL RRQ[Q RWLWZ R\\G\\J ReReY R\\GkGkJ RhTlX RgPjW RcCdG RUCRJ RcKaM[O RcOaR[T RZXUZ ReR`W[Y Rd[`[ RjNdPeW',
	    'PoTH[H RTLZL R`OkO RRQ[Q R]UlU RWLWZ R]G]J R`K`U RfOfU R]GlGlJ RgXjY RdCeF RUCRJ R[XUZ RbX\\Z RiJ`K',
	    'PoTG[G RTK[K RRQ[Q R_ZhZ RWKWZ R]F]J R_S_[ R_ShSh[ R]FkFkJ RcMiSlT RfJjN RcCdF RcM\\T R`J\\N RVCSJ R[XVZ',
	    'Po]DlD RTG[G RTL[L R]MkM R^QkQ RRQ[Q R]TlT Ra[d[ RWLWZ R]H]M RbDbM RdTd[ RfDfM R]HkHkM RhVkZ R`V]Z RVCSI R[XVZ',
	    'PoTH[H RTL[L RRQ[Q R_RiR R^VkV R\\[l[ RWLWZ R]I]M R_M_R RdRd[ RdCdI R_MiMiR R]IlIlM R_D`H RjDhH RVCSI R[XVZ',
	    'PoTG[G R_GjG R_JjJ RTK[K R]QkQ RRQ[Q RWKWZ R_D_J R_DjDjJ RVCSI R[XUZ RbMbQ RfMfQ R_SbXgZl[ R]MkMkQ R]M]Q R]TjTgWcY[[',
	    'PoTG[G R^HkH RTKZK R^LkL RRQ[Q RcTkT R^ThT Ra[d[ RWKWZ R^D^L RdDdL ReTe[ R^DkDkL RhWkZ RhQlU R`W\\Z RVCSI RcL^PeP]T R[XVZ',
	    'PoTHZH R\\HlH RTLZL R\\ObO RfOlO RRQZQ R\\TbT ReTlT R_[b[ Ri[l[ RWLWZ R\\KbKb[ RdCdH RkDkH RfKlKl[ RVCRJ RZXUZ R\\E\\H R\\K\\T[Z RfKeUd[',
	    'Po^FlF RTH\\H RTL[L R_LlL RaNjN RSQ\\Q R`TkT R`WkW RXLXZ R`Q`[ ReEe[ RaIjIjN RVCSI R[XVZ R`QkQk[h[ R^F^R\\[',
	    'Po^FkF RTG[G R]KlK RTKZK R_QjQ RRQ[Q R_TjT Rf[l[ RWKWZ R_M_T RgTg[ R_MjMjT RlXl[ RdCdF RaGaI RUCSJ RhGfJ R[XUZ RaY\\[ RbTaY',
	    'Po]FlF RTG[G RTKZK R_OiO RRQ[Q RaWhW Rh[k[ RWKWZ R]K][ RaSaY RdKdR RaShShW R]KkKk[ R`GaJ RdCdF RVCSI RhFgK R[XVZ',
	    'PoTHYH RcHlH RZHaH RSKYK RdMkM RRQZQ RcSlS RVKVZ R\\D^F RfWb[ RgMgTk[ RhSdY RUCSJ RYXUZ R_[\\[ RdCbK ReJcQ R\\M`M`W_[ R\\H\\RZ[ RgPfW',
	    'PoTGYG RdHlH RZHaH RRQYQ RgRkR RTLWLVZ R`M`Y ReXhZ R\\D_F RdSeY RUCRJ RYXUZ R\\M`M RhLgZlZ RdCbK R_[\\[ RdQb[ RdLkLjP R\\H\\Q[Y',
	    'PoTGZG RTLZL RRQZQ RVLVZ ReDjDjM R]G_H ReGgH RdMfPlR RVCSI RbI\\K RjIdK R]DbDcL`P[R RdR^T RZXUZ RfT_X RhWcY][',
	    'Po\\DlD RTH[H RTL[L R]LkL RRQ[Q R^QjQ R^TjT R]WlW RWLWZ R]H]L R^N^T RbDbL RdTd[ RfDfL R^NjNjT RVCSI R[XVZ R]HkHkL',
	    'Po\\ElE RTHYH R]KeK RRQZQ R^TdT RTLWLVZ R^M^T RaKaW RdMdT RgLkLkP RYXVY R_G`J RhTe[ RUCRJ RcGbK RgGfP RiOjY R\\E\\SZ[ RiNgY',
	    'Po]GkG RTG[G RTLZL R_PiP RRQ[Q R_TiT Ra[d[ RWLWZ R`L`S RdTd[ R^MiLiS RhVkZ R^I`K RfHlN RjHhK R`V\\Z RbI\\N RVCSI RdCaK R[XUZ',
	    'PoTG[HlI RTKZK R]MkM R]QjQ RRQ[Q R\\TlT RWKWZ ReCeI R^D_G RiDhG RdSeW RbCaI`K RdMdUhYl[ RVCSI RgJeM R[XUZ RcV`Y[[',
	    'PoTG[G R^KkK RTLZL R^NkN R]QlQ RRQ[Q R_WjW R_ZjZ RWLWZ R_G_N R_S_[ RfHfN R_SjSk[ R]IkHkN RbC\\I RaFgEeI RVCSI R[XUZ',
	    'Po[GcG RTGYG RTLYL R\\MbM RSQZQ R[[^[ RWLWZ R\\I\\M R_S_[ R\\IbIbM RfVb[ R[PbP_S R^C_F RfRjZ RiPeY RUCSJ RZXUZ ReJhW RfCdN ReIkIhU RcU[V',
	    'PoTGZG R_JiJ RSLYL RRQZQ R_SiS Rh[l[ RVLVZ R\\I\\[ R_M_S RdHdY R_MiMiS RaElEl[ RdRiW R\\D_G RdR_X RbNbQ RgOfQ RUCRJ RZXUZ',
	    'Po]GkG RTG[G RTL[L R]NlN RRQ[Q R^UiU R^YiY RWLWZ R^Q^[ RbGbN RgFgN R^QjQj[ R]I`K R`DbE RjIiL RVCSI R[XVZ',
	    'PoTH[H RTL[L R_PjP RSQ[Q R^XlX RWLWZ R^S^Y ReJe[ R_LjLjP R^SlSlY R]F]GlFlI RdCeF RgHlJ RVCSJ RcH]J R[XVZ R_L_P',
	    'PoTGZG RcJjJ RTKYK RRQZQ RbTkT RbZlZ RVKVZ RbNbT RcDcJ RgJgZ RcDjDjJ RbNkNkT RjVl[ RUCRJ RZXUZ R_[[[ R[D`D`J\\J\\Q`Q`V_[',
	    'PoTH[H R`LhL RTLZL RRQ[Q R_TiT R\\[l[ RWLWZ R_O_T R_OiOiT RgIlM RiDfG RkHhI R]G`I\\M R`VaZ ReDhJ RgVe[ RVCSJ R]EbE`K R[XVZ',
	    'Po]ElE RTG[G R^KkK RTKZK R_MjM R_OjO RRQ[Q R_RlR R]UjU RWKWZ R_I_S RaCaG ReIeR RgCgG R_J\\M RVCSI R[XUZ R_UaWfYl[ RiVdY\\[',
	    'Po^EkE RTG[G R_KbK RgKjK RTLZL R_NcN RgNkN RRQ[Q R_VkV R_ZkZ RWLWZ R^H]K R_R_[ ReReZ ReEeP R_RkRk[ R]HlHlK RVCSI R[XVZ',
	    'PoTHZH R]IjI RTLZL R\\LlL RRQZQ R^SjS RWLWZ R]D]I R^N^S R`V`[ RaDbI RfDfI R^NjNjS R]DjDjI ReUjZ RdSfW RVCSJ RYXUZ RcT[W RkUfW RfZ_Z',
	    'PoTG\\G R^IlI RSK[K RRQ[Q R_UhU R^YgY RWKWZ R^D^I RcDcI RdNdX RgEgI R^DlDlI RgVhY RYXV[ RaK]O RVCSJ Rh[k[g[ R_PhPhU R_P^U R_MlMlVk[',
	    'Po]FlF RTG[G R_JkJ RTKZK R_QlQ RRQ[Q R`TjT RWKWZ RcIc[ RgJg[ R`MjMjT RgUlZ RcCdF RhGgK RcU^Z RVCSI R[XUZ R]F]V\\[',
	    'Po]FkF RTH[H R\\KlK RTLZL R_PjP RRQ[Q R_SjS RWLWZ R_M_S R_MjMjS RcUfX RdCdF RaGaJ RjVkZ RVCSJ R]W\\Z RhGfJ R[XVZ RaVa[hZh[',
	    'PoTH[H RTLZL RaPgP RRQ[Q R^TjT Ra[d[ RWLWZ RdTd[ R\\F\\GkFkH R^N`O RhVkZ RgMlR RcCdF R_LbM\\S R`V\\Z R_J\\M ReIhO ReJjJhN RVCSJ R_JcJaO R[XUZ',
	    'Po\\FlF RTGZG RSKYK R\\MkM R_PdP RRQZQ RfTlT RVKVZ R_N_U RaFaM RcWcZ RgNgT RfFfM R\\IkIkM RdCdF RYWUY RfWgZ RjWl[ RUCRJ R_W][ RkPfQ RdT^T R\\F\\Q[Z',
	    'PoTGZG RTKZK R\\PjP RRQZQ RWKWZ R\\J\\P R]R][ RcRc[ R\\JjJjP RlYl[ R\\F_H RaFdH RfFiH R_C\\F RcCaF RhCfF R_KgO ReSgS R_SaS ReVgV R_VaV RUCRJ RgK_N RZXUZ R`Y\\Z RgYbZ RiSj[',
	    'PoTGZG R]JjJ RTLYL ReOlO RRQZQ R]WjW Rd[l[ RWLWZ R]R]V R^F^J R\\GjGjJ R\\SjSjW R^K_O\\S R`C\\G R_EfEdG ReKeO RgXiY RUCRJ RZXUZ R_QfQdSbW`Y\\[ RdWd[ RkLeM R^LcM]N',
	    'Po\\FlF RTHZH RTLZL ReLkL R\\NkN RSQZQ R]QkQ R[TlT RWLWZ R`V_[ R]H]LaL`T R]HbHbK ReHfLgT ReHkHkL RcCdF RZXVZ ReUhYl[ RUCRJ RdT\\X RkVgX ReZ_Z',
	    'PoXDiD RXHhH RXLhL RRQlQ RXDXZ R^ScXj[ RjRbV RaYT[',
	    'PoWEiE RWIgI RWMgM RRQlQ R_YiY R]YfY R[YdY RXZbZ RVZ`Z RSZ^Z RWEWQ RdUk[ R[QWZ',
	    'PoUE^E RUI]I RUM]M RRQ_Q ReT^[ RhSk[ RhMcW RgOjY RUEVQTX RcC`O RcHiHeT R\\S^XRY',
	    'PoUE]E RUI\\I RUM\\M R^NkN RRQ]Q RgWl[ RbV][ RdEeRiY RdQaX RUEVQSY RdLgW RdNbW RjD^F RZS\\XRX RdJeT',
	    'PoUE]E R`IkI RUI\\I RUM\\M R^PkP RRQ]Q RgWk[ RcU^[ ReCeQhY RePbX RUEVQSY RaE_M ReNfW R[S\\XRX ReLdU',
	    'PoUE\\E RUI[I R_JlJ RUM\\M RRQ]Q RbRlR RaZlZ RaTaZ RbLbR RfKfZ RlLlR R_DlDlI RlTl[ RUEVQSY RZT[XRX R_D_R]Z',
	    'PoUE\\E R]GlG RUI[I RUM[M R_PiP RRQ\\Q R_SiS Ra[d[ R`L`S RdSd[ R^MjLiS RhVkY R^I`K RfHlN RkIhK R`V\\Y RbI]N RUEVQSY RdC`K RYT[XRX',
	    'Po\\DkD RTEZE RTIZI RTMZM RRQ[Q Rh[j[ R]J][ RdDd[ R]JjJj[ R_NbP ReMhP RiGlJ R_F\\J RhLeR RhSeY RaL_R RaS_Y RTEUQSX RYSZXRY',
	    'PoRNlN RXCX[ R_NaSfXlZ RgDcH\\K R`YWZ',
	    'PoRI\\I RaIlI RRM\\M RaMlM Rf[l[ RRER[ RRE\\E\\M RaEaM RaElEl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL Rh[l[ RRER[ RRE]E]K RaEaK RaElEl[ RaN_S[WWX',
	    'PoRI\\I RaIlI RRM\\M RaMlM RWSgS Rf[l[ RRER[ RRE\\E\\M RaEaM RaElEl[',
	    'PoRI\\I RaIlI RRM\\M RaMlM Rf[l[ RRER[ RRE\\E\\M RaEaM RaElEl[ R_OaTcWgX R_O\\TZWUX',
	    'PoRHWH RZH_H RRLWL RZL_L R\\Z_Z RRER[ RREWEWL RZEZL RZE_E_Y RgRkZ RdQaZ ReCeNiX RfLbX ReHhV ReJdU',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWOgO RZY_Y Rg[l[ RRDR[ RRD\\D\\L R`O`Y RaDaL RaDlDl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWPgP RYTeT RWXgX Rg[l[ RRER[ RRE\\E\\L RaEaL RaElEl[',
	    'PoRI\\I RaIlI RRM\\M RaMlM RWPgP Rf[l[ RRER[ RRE\\E\\M R_P_Z RaEaM RaElEl[ R_SfU',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWOgO RVThT Rg[l[ RRER[ RRE\\E\\L R_O_Z RaEaL RaElEl[',
	    'PoRI\\I RaIlI RRM\\M RaMlM RWQgQ R]ZaZ Rf[l[ RRER[ RRE\\E\\M RaEaM RaNaZ RaElEl[ RaQ]UVX',
	    'PoRI\\I RaIlI RRM\\M RaMlM RXWfW Rf[l[ RRER[ RXQXY RRE\\E\\M R_O_W RaEaM RfQfW RaElEl[',
	    'PoRH\\H RaHlH RRM\\M RaMlM RWOgO RVThT Rg[l[ RRER[ RRE\\E\\L RaEaL RbOb[ RaElEl[ R[P[WYZW[',
	    'PoRH\\H RaHlH RRM\\M RaMlM RVPhP RcZgZl[ RRER[ RRE\\E\\L R_N_P RaEaL R[ScScY RgWgZ RaElEl[ RZVWZ R[SYX',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVOhO R_ZcZ Rg[l[ RRDR[ RRD\\D\\K R_M_O RaDaK RaDlDl[ R[VWZ R\\OZX R\\SeSdZ',
	    'PoRH\\H RaHlH RRK\\K RaKlK RVOhO R\\XfX RZXcX RRDR[ RRD\\D\\K RaDaK RZTUX RdUfY R\\NXW R_R\\X RaDlDl[h[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWPfP RXTfT RWYgY Rg[l[ RRER[ RRE\\E\\L R_P_X RaEaL RaElEl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL Rh[l[ RRER[ RRE]E]K RaEaK R^N^YgXgY RaElEl[ RdMXO R`QVR RbQYR ReP\\R R_UWV RaTYU ReT]U',
	    'PoRI\\I RaIlI RRM\\M RaMlM RVQhQ Rf[l[ RRER[ RRE\\E\\M R_N_Z RaEaM RaElEl[ R_PXX R_PeWhX',
	    'PoRH\\H RbHlH RRL\\L RbLlL RZRdR RZVdV R_ZdZ Rf[l[ RRER[ RZOZX RRE\\E\\L RbEbL RZOdOdZ RbElEl[',
	    'PoRI\\I RaIlI RRM\\M RaMlM RYTdT RYYdY Rf[l[ RRER[ RYPYZ RRE\\E\\M RaEaM RYPdPdX RaElEl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVPhP Rg[l[ RRER[ RRE\\E\\L R_M_P RaEaL RaElEl[ RZQ_WgY RdQ`V\\XVY',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXOgO RXShS R_ZdZ Rh[l[ RRDR[ RXPXS RRD]D]L RaDaL RdOdZ RaDlDl[ RbTWY',
	    'PoRH\\H RaHlH RRL\\L RaLlL R[Z_Z Rg[l[ RRDR[ RRD\\D\\L R_N_Z RaDaL RaDlDl[ R[SVX ReRaS RfOdR R_NaSdWhX RVQ\\QYV',
	    'PoRH\\H RaHlH RRK\\K RaKlK Rh[l[ RRDR[ RRD\\D\\K RaDaK RcMcZ RaDlDl[ R[N_P RXQ]S R_VWV RcUZV RfU_U',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXRfR RXVfV Rh[l[ RRER[ RXOXW RRE]E]L R_O_[ RaEaL RXOfOfW RaElEl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVOhO RcYfY Rg[l[ RRER[ RXSXY RRE\\E\\L R_M_Z RaEaL RXSfSfY RaElEl[',
	    'PoRH\\H RaHlH RRK\\K RaKlK RWOgO RUXhX Rh[l[ RRER[ RRE]E]K RaEaK RaElEl[ R_M_O RZQ\\V RdQaW',
	    'PoRH\\H RaHlH RRK\\K RaKlK RWNgN RVUhU Rg[l[ RRDR[ RRD\\D\\K R_N_[ RaDaK RaDlDl[ RYPZS ReQcS',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVOhO RXV`V Rg[l[ RRER[ RXRXW RRE\\E\\L RaEaL RaElEl[ RXR_R`V RdOdY`Y',
	    'PoRH\\H RaHlH RRK\\K RaKlK R[QcQ RbYfY Rh[l[ RRER[ RRE]E]K R^T^Z RaEaK RXTfTeX RaElEl[ R`NhR R]NVR',
	    'PoRH\\H RaHlH RaLlL RVPfP RUTiT RRDR[ RRD\\D\\K RaDaK RbRbZ R[WWZ RRL\\LYP RaNgP RaDlDl[h[ R[RZX',
	    'PoRH\\H RaHlH RRK\\K RaKlK R[YcY RRDR[ R\\P\\Y RRD\\D\\K RaDaK R]M`P RdQhU RcVcY RWQUV RdN^UVY RaDlDl[h[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXOfO RXSfS RWXgX Rg[l[ RRER[ RRE\\E\\L R_O_X RaEaL RaElEl[ RcUeW',
	    'PoRH\\H RaHlH RRK\\K RaKlK RVNhN RRDR[ RRD\\D\\K RaDaK RcWfZ RaWdX RWQ\\S R^NZR RbP`RWV ReSbUXZ RaDlDl[h[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWQgQ RVThT Rh[l[ RRER[ RRE\\E\\L RaEaL RaElEl[ RZN[Q RcNbQ R_TbXgY R_T\\XWZ R_Q^W',
	    'PoRH\\H RaHlH RRL\\L RaLlL RYZeZ Rg[l[ RRDR[ RZUZ[ RRD]D]L RaDaL RWVeVe[ RaDlDl[ R\\NVQ R[PbThU R[OdOaR\\TVU',
	    'PoRH\\H RaHlH RRL\\L RaLlL R[QcQ RYYeY Rh[l[ RRER[ RYTYZ RRE]E]L RaEaL RYTeTeZ RaElEl[ R`NhR R]OVR R^M`M',
	    'PoRH\\H RaHlH RRL\\L RaLlL R_QgQ R[RbR Rh[l[ RRER[ RYPYZ RRE]E]L RaEaL RaElEl[ RcNeO RZOUT RcYgZ RhXgZ ReSbW\\Y R_NbX',
	    'PoRH\\H RaHlH RRL\\L RaLlL Rg[l[ RRER[ RRE\\E\\L RaEaL RaElEl[ RbUfZ RZU^W RaU]Z R`OhR RbS`X R^M]OVR R[SXYUZ',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWPgP RVUhU RRER[ R[N[U RRE\\E\\L RaEaL RcNcU RbVfZ R[VXZ RaElEl[g[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RYOeO RWRgR RXVeV RWZhZl[ RRDR[ RRD]D]L R_M_R R_T_Z RaDaL RaDlDl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXTfT RVYgY Rh[l[ RRER[ RXPXT RRE\\E\\L R_M_Y RaEaL RXPfPfT RaElEl[ RdVgZ',
	    'PoRG\\G RaGlG RRK\\K RaKlK RVMhM RaUgU RWXgX Ri[l[ RRDR[ RWQWX RRD\\D\\J RbMbU RaDaK RWQgQgX RaDlDl[ R\\M\\TXV',
	    'PoRH\\H RaHlH RRL\\L RaLlL RZRdR RXYfY Rg[l[ RRER[ RXNXZ RRE\\E\\L R_P_W RaEaL RXNfNfZ RaElEl[ R_QcV R_QZV',
	    'PoRH\\H RaHlH RRK\\K RaKlK RYQeQ RYSeS RRDR[ RYNYY RRD\\D\\K R_L_N RaDaK RYNeNeS RbWeZ R]UcX RaDlDl[h[ ReVaXWZ',
	    'PoRH\\H RaHlH RRL\\L RaLlL RYReR RXYfY Rh[l[ RRER[ RXUXZ RYNYR RRE]E]L RaEaL RYNeNeR RXUfUfZ RaElEl[ R^R^U',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ R^QhQ R]ViV R]YiZl[ RRDR[ RRD\\D\\J RaDaJ RcMcV RaDlDl[ RZUUZ RUM[MUR[RXXbY RgL_M',
	    'PoRH\\H RaHlH RRK\\K RaKlK Rh[l[ RRDR[ RRD\\D\\K RaDaK RbMbT RaDlDl[ RWMZN RfOiR RZVVZ RUQYR R^O\\T ReV]Y',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXS]S R`SfS RXXfX Rg[l[ RRER[ RXPXX RRE\\E\\L RaEaL R`OfOfX RaElEl[ R]OXP',
	    'PoRH\\H RaHlH RRL\\L RaLlL RYUeU Ri[l[ RRER[ RYPYU RRE\\E\\L RaEaL RXRfQeU RaElEl[ R]OcNgR RZOWR R[V[XV[ RaUaZgZg[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXUeU Rh[l[ RRDR[ RRD\\D\\L RaDaL RXPePeU RaUaZfYfZ RaDlDl[ RZN]P RcNaP RYPXU R\\U[XVZ',
	    'PoRG\\G RaGlG RRK\\K RaKlK RUMhM RWRgR RWUgU RUWiW Rh[l[ RRDR[ RWPWT RRD\\D\\J R_L_Z RaDaJ RWPgPgT RaDlDl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RYQeQ RYTeT RYWeW Rh[l[ RRER[ RYOYV RRE\\E\\L RaEaL RYOeOeV RaElEl[ RbXgZ R\\XVZ',
	    'PoRH\\H RaHlH RRK\\K RaKlK RYPgP RYRfR RYVfV RYYgY Rh[l[ RRER[ RZOZZ RRE]E]K RaMaY RaEaK RaElEl[ R[MVR',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RYOdO RYQdQ RXVfV RXYfY Rh[l[ RRDR[ RXTXY RYMYQ RRD\\D\\J RaDaJ RYMdMdQ RXTfTfY RaDlDl[',
	    'Po]HcH RfHlH R]LcL RfLlL RRP[P RS[W[ Ri[l[ RWLWZ R]D][ R]DcDcL RfDfL RfDlDl[ RVF[K RTEVG RdRiV RZDSL RZR[V RTRSX ReOaW',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVOiO RWR\\R RaUgU RUYiY Rh[l[ RRER[ R\\O\\R RWRWU\\U\\Y RRE]E]L RaEaL RaUaY RaOaRgRgU RaElEl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVNhN RYUeU RYXeX R_[f[ Rh[l[ RRDR[ RZQZY RRD]D]K R`P`[ RaDaK RXReReX RaDlDl[ RbNeRiS R]MYRUS',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RXOeO RXQeQ RVThT Ri[l[ RRDR[ RXLXQ RRD\\D\\J R^Q_T RaDaJ RXLeLeQ RaDlDl[ RZT\\V`XgZ RcV^XWZ',
	    'PoRH\\H RaHlH RRL\\L RaLlL RYV]V R`VeV RYYeY Rh[l[ RRER[ RYSYZ RRE]E]L RaEaL R`SeSeZ RaElEl[ R\\MVS R]RXS R[OeNdQ`Q',
	    'PoRH\\H RaHlH RRL\\L RaLlL RUP_P RZZ\\Z Rh[l[ RRER[ RRE]E]L RXT]S\\Z RaEaL RaElEl[ RbSeT RcMhR RcN^R R`WfZ RZMYPWY',
	    'PoRH\\H RaHlH RRK\\K RaKlK RXNaN RXPgP RYWeW RYZeZ Rh[l[ RRER[ RYUYZ RRE\\E\\K RaEaK RXNWS[TeUe[ RaElEl[ RaRfS RcM]M',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVOhO RWU]U Rh[l[ RRER[ RRE]E]L RaEaL RaElEl[ RhWgZ RcMfN RcYgZ ReS`Y\\Z RWRWV RWR]R]V R`MbX R^XVY',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWMaM Rh[l[ RRDR[ RVQVS RRD\\D\\J RaDaJ RVQgQgS RaDlDl[ RcMbQ RYNYP R[T]WaYgZ RYTdTaW^XWZ ReL_L',
	    'PoRH\\H RaHlH RRJ\\J RaJlJ RVNiN RUQjQ RYVeV RZZhZl[ RRDR[ RZSZY RRD\\D\\J RaEaJ RYSeSeW RaElEl[ RdLdRjT R[L[RTU',
	    'PoRH\\H RaHlH RRK\\K RaKlK RWP]P R`PgP RWS]S R`SgS RcZgZ Ri[l[ RRER[ RWNWZ RRE]E]K RWN]N]S R`N`S RaEaK R`NgNgY RaElEl[',
	    'PoRG\\G RaGlG RRK\\K RaKlK RWOgO RUQhQ RZYfY Rh[l[ RRDR[ RZSZY RRD\\D\\J RaDaJ RaDlDl[ RZL\\O RcRiU R_L]PZSUU RYTcTbW_W',
	    'PoRH\\H RaHlH RaKlK RRK\\K RYPeP RYReR RYTeT RWVgV Rh[l[ RRDR[ RYMYT RRD]D]K RaDaK RYMeMeT RaDlDl[ R_VbYgZ R_T]YWZ',
	    'PoRH\\H RaHlH RRK\\K RaKlK RUMiM RVRhR RTUjU Rh[l[ RRDR[ RVOWR RRD\\D\\K RaEaK RbMbR RVOgOgR RaElEl[ R]M]SXWgY RfU`XVZ',
	    'PoRG\\G RaGlG RRI\\I RaIlI RXMfM RXOfO RXQfQ R^ShS RVVgV Rh[l[ RRDR[ RXKXQ RRD\\D\\I R^Q^U RaDaI RXKfKfQ RaDlDl[ RXWgY ReW^YVZ',
	    'PoRH\\H RaHlH RRK\\K RaKlK RZQdQ RWY]Y RaYgY RRDR[ RWTWZ RRD\\D\\K RWT]T]Z RaTaZ RaDaK RaTgTgZ RZMcMcQ RZMZQ RaDlDl[h[',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWNgN RVRhR RYWeW RYZeZ Rh[l[ RRDR[ RYTY[ RRD]D]K RaDaK RYTeTeZ RaDlDl[ RcNbR RZN[R',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RVOhO RYTeT RYVgV RVXiX Ri[l[ RRDR[ RYVXX RRD\\D\\J R`T`Z RaDaJ RXMdMeO RYQYReQeT RaDlDl[ R]K]O',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWMhM RWSgS RXVfV RVYhZl[ RRDR[ RWOWS R\\M\\S RRD\\D\\J R_S_Y RaDaJ RaMaS RWOgOgS RaDlDl[',
	    'PoRH\\H RaHlH RRL\\L RaLlL R[RhR R]YfY Rh[l[ RRER[ RRE]E]L R]U]Z RaEaL RbNbU R]UfUfY RaElEl[ RWNZO RZVVZ RVQYR RfN]O',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWUfU Rh[l[ RRDR[ RRD\\D\\J RaDaJ RaDlDl[ R`LeQ ReMbN RgOdP RXM^MYQ RZRcQiR R^VfZ RXN[PUS R_U\\XWZ R_R^W',
	    'PoRH\\H RaHlH RRL\\L RaLlL RWNgN RXUfU Rh[l[ RRER[ RRE]E]L R_L_[ RaEaL RXPfPfU RaElEl[ R[R\\S RcRbS R_TbWgY R]U[XVZ RXPXU',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RUQhQ RZVeV RZZeZ Rg[l[ RRDR[ R[S[Z RRD\\D\\J R]K]P RaDaJ RYTeTeZ RaDlDl[ RaRcR ReLbO RWMcNVU',
	    'PoRG\\G RaGlG RRK\\K RaKlK RXPfP RXRfR RUU]U R\\Z_Z RRDR[ RXMXR RRD\\D\\J R_R_Y RaDaK RXMfMfR R_ShW R\\UUX RgTcU RaDlDlZh[',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RYUdU Rg[l[ RRDR[ RYQZU RRD\\D\\I RYO_O_X RaDaJ RYQdQdU RWLgLgW RaDlDl[ RYY_XdWdY RiVhYiY RWLWSVY RcN\\O',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RYL]MgN RVWhW Ri[l[ RRDR[ RVOVW RYKYN RRD\\D\\J RaDaJ RZPePeU RaDlDl[ RaReR RaYhZ R\\YVZ R]R`Q R_UbU RbR_Q R`U]U RZQ^QZQZU\\TeTaU RdK^L',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RYNeN RYQeQ RYSeS RVUhU Rh[l[ RRDR[ RYLYS RRD\\D\\J R_S_Z RaDaJ RYLeLeS RaDlDl[ R`VhZ R]VVZ',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ R[OcO R[QcQ RZZ]Z RdZgZ Ri[l[ RRDR[ RYMYQ RRD\\D\\J RUS]S]Z RaDaJ RYMeMeQ R_SgSgZ RaDlDl[ RZTUV R[WUY RdT_V RdW_Y',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWQgQ RYVeV RVYhY Ri[l[ RRDR[ RYSYV RRD\\D\\J RaDaJ RYSeSeV RXNfMeO RaDlDl[ RbWaY R[W\\Y RXLXO R_L_O',
	    'PoRH\\H RaHlH RRL\\L RaLlL RXNfN RVPhP RVYhZl[ RRDR[ RRD]D]K RaDaK RaDlDl[ R]PZS R\\U]Y R_L_P RWT_UfUfY RcRgT RaUaY RXUXY',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVP_P RVU_U Rh[l[ RRER[ RVRVV RRE]E]L R_R_V RaEaL RaPhPgS RaElEl[ ReVhY RXMXP RZPZWWZ RcV`Z R]N]P RbM`S RdPfX RdPcW',
	    'PoRH\\H RaHlH RaKlK RRK\\K RXNfN RXPeP RXReR Rh[l[ RRER[ R[W[Z RRE]E]K R_N_U RaEaK RaElEl[ RbVcX RWWVZ R_W_Y RXNXUfUfZbZ',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ R`TgT Rh[l[ RRDR[ RRD\\D\\J RaDaJ RaDlDl[ RfVhX RaVbY RgQhT RXVVY RcL`P RgN`T RZLVP R\\V]Y R]MVT]S]U R\\OVP RfO`P',
	    'PoRH\\H RaHlH RRL\\L RaLlL RUO_O R`QiQ RVU^U RcZfZ Rh[l[ RRDR[ RVQVU RRD]D]L RVQ^Q^U RaDaL RfNfZ RaDlDl[ RaScW RXWWY R\\V[YUZ R^YXZ',
	    'PoRH\\H RaHlH RaJlJ RRK\\K RWS]S RXY^Y Rh[l[ RRDR[ RWMWVXZ RRD]D]J RWO]O]R RXU^U^Y RaDaJ RaDlDl[ RcThY ReT`Z RaPdW RaPhPdW RbL_S R]MWM RWOVY',
	    'PoRH\\H RaHlH RRK\\K RaKlK RVP]P R^PgP R^SgS RUS]S R^UgU RdZhZl[ RRER[ RRE\\E\\K R^N^U RaEaK R^NgNgU RaElEl[ RYMYV]W R`W]Z RdUdZ RWYUY',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RYTeT RXVfV RVYhZl[ RRDR[ RVNVQ RYQYT RRD\\D\\J R_K_N R_T_Y RaDaJ RYQeQdT RVNhNgQ RaDlDl[ RYL\\N RdLbN',
	    'PoRH\\H RaHlH RRL\\L RaLlL RVS^S R_ShS RaXfX Rh[l[ RRER[ RRE]E]L RaUa[ RaEaL RfUfX RaElEl[ R_NbQ RWNZQ RcM`O ReNaS RXUXX]XZ[ R[MXO R]NXS R]U\\Y',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWP^P RWS^S Ri[l[ RRDR[ RRD\\D\\J RWM^M^U RaDaJ RaDlDl[ RcThY RWMWU]V^X RXWUZ RfT`Z RaPdW RaPhPdW RbL_S',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RVM_M RWR]R RWU]U Ri[l[ RRDR[ RWPWX RZMZO RRD\\D\\J RaDaJ RaDlDl[ RcThY ReT`Z RaPdW RbPhPdW RcL_S RUP^P]YVX',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RZRdR RZZ]Z RdZgZ Ri[l[ RRDR[ RRD\\D\\J RVT]T]Z RaDaJ RZPZQdPdR R_TgTgZ RaDlDl[ R_KWN RZUVV R[WUY RcU_V RdW_Y R[NaMhN',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RXNfN RUPiP Ri[l[ RRDR[ RRD\\D\\J R_K_P RaDaJ RXLXMeLeN RaDlDl[ RaYgZ R\\YVZ R`WcW RXW\\W RaS^S R[SXSXVdTfSbS R^W[W RaW^W RXRfRfWbW RXS\\S^SaS R]W`W',
	    'PoRH\\H RaHlH RaKlK RaPfP RXTeT RXVeV RVXgX Rh[l[ RRDR[ RXQXV RRD]D]K RRKZL]M]O R_Q_[ RaMaO RaDaK RXMXP[PeQeV RaMfMfO RaDlDl[',
	    'PoRH\\H RaHlH RaKlK RRK\\K R_OhO RWQ]Q R_SiS R_WhW RXY^Y Rh[l[ RRDR[ RWNWUXZ RRD]D]K RWN]N]Q RXU^U^Y RaDaK RdMdO RdSd[ RaDlDl[ RaPbR RfPfS RWOUY',
	    'PoRG\\G RaGlG RRK\\K RaKlK RZRdR RXWfW RXZfZ Rh[l[ RRDR[ RVNVQ RXUXZ RRD\\D\\J R_K_N R_U_Z RaDaJ RZPZQdPdR RXUfUfZ RVNhNhQ RaDlDl[ RYL[N ReLcN',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWNgN RUPiP RXUfU Ri[l[ RRDR[ RWLWN RXRXU R\\L\\N RRD\\D\\J R_U_[ RaDaJ RaLaN RWLgLgN RaDlDl[ R^VgZ R_UWY RXRfRfVcX',
	    'PoRH\\H RaHlH RaKlK RRK\\K R]MfM R[OhO R\\RgR R]TfT R[WhW R\\ZhZl[ RRDR[ RRD]D]K RaDaK RbLbO RbRbX RaDlDl[ RVSYSYXVY ReOdR R^O_R RWLZM RVPYP RZYaZ',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ RWNfN RWPfP RVRhR RXWeW RUYiZl[ RRDR[ RWLWP R\\K\\P RRD\\D\\J RaKaP RaDaJ RXTXUeTeW RWLfLfP RaDlDl[',
	    'PoRG\\G RaGlG RRJ\\J RaJlJ R\\MgM RZOiO R\\ThT R^ZiZl[ RRDR[ RRD\\D\\J RaDaJ RaTaX RbKbO R\\Q\\RhQhT RaDlDl[ RVLXN RTRYRYWTY RaUiW RXWaY RcT[XcY',
	    'Pog[l[ RRGR[ R_C_M RRGlGl[',
	    'PoWPgP Rf[l[ RRHR[ R[DlDl[ RTDWG',
	    'Pof[l[ RRJR[ R[ElEl[ RTDXH R_MgV R_NWV R`I\\R',
	    'PoXKgK RYPeP RWVgV Rf[l[ RRJR[ R[ElEl[ RTDWG',
	    'PoWKgK RURhR Rf[l[ RRHR[ R_K_Z R[DlDl[ RTDXG',
	    'PoWLhL R]YbY Rh[l[ RRIR[ RcHcX R[ElEl[ RSDVG RcL\\RUV',
	    'PoZTdT Rf[l[ RRIR[ RZKZV RZKdKdT R[ElEl[ RSDWH',
	    'PoVTcT R_YdY Rg[l[ RRHR[ R[DlDl[ RTDWG RXIdIcP RYKYPgPeY',
	    'PoWJgJ RXQfQ RWWgW Rf[l[ RRIR[ R_J_W R[ElEl[ RTCXH',
	    'PoVJhJ RXNgN RcXgX Rf[l[ RRJR[ R_H_Z R[ElEl[ RTCXG RVShSgX',
	    'PoWMhM Rf[l[ RRJR[ R_I_Y R[ElEl[ RTDXH R_MgU R_L]OWU',
	    'PoVLhL R^WfW R\\WdW Rf[l[ RRHR[ R[DlDl[ RTDXH RZQVU RdSfX R]IXS R`N\\W',
	    'PoYQeQ RYVeV Rh[l[ RRIR[ RYKYV RYKeKdV R[ElEl[ RSDVG',
	    'PoVKhK Rf[l[ RRHR[ R[DlDl[ RTCWG RbUfX R_H`K RWO`SdV RdLbQ]UVX',
	    'PoVLhL RcXiX RRIR[ R[OcOdX RSDVG R^H`J RYWVX RhViX R[ElEl[h[ R[OZV',
	    'Po[WdW Rf[l[ RRHR[ R\\L\\V R[DlDl[ RTDWG R]HaL RfLhT RWMUT RdRdW',
	    'PoXOgO RXSgS Rh[l[ RRIR[ RXJXT R_J_Z RXJgJfT R[ElEl[ RSDVG',
	    'PoVKhK RcWfW Rf[l[ RRIR[ RXOXW R_K_Z RXOfOfW R[ElEl[ RSDWG R^H_J',
	    'PoXJeJ RVOhO RXSfS RVXhX Rf[l[ RRHR[ R_Q_X R_H_O R[DlDl[ RTDWG',
	    'PoVJgJ RZNdN RZRdR R]VhV RYVeV RUVbV Rg[l[ RRIR[ RZJZV RdJdY R[ElEl[ RSDVG',
	    'Po\\LhL R]XiX Rh[l[ RRIR[ R[ElEl[ RSDVG RWIZL RaOgU RaP\\U RVPYPZVVW RYWcX RbIaQ',
	    'PoWRgR R_WgW R\\WcW RZWaW RVX_X Rh[l[ RRIR[ RWLWQ R_H_W RWLgLgQ R[ElEl[ ReThX RSDWH',
	    'PoYNeN RXWgW Rf[l[ RRHR[ RXQXX RYIYN RYIeIeN RXQgQgW R[DlDl[ RTDXG',
	    'PoWLgL RXSfS RXWhW Rf[l[ RRIR[ RWIXL R_G_L RgIfL R[ElElZ RSDVG RhUhW RWOfOfS RXSXW',
	    'Po`NhN R\\NdN Rh[l[ RRIR[ RYKYY R[ElEl[ RSDVF RdIfK RYLUP RdWgX RZIWO RgPbU\\X R`IcV',
	    'PoYXeX Rf[l[ RRHR[ RYSYY RYSeSeY R[DlDl[ RTCXG RYKVM R\\HXL R[J^McPiQ R[IeIbM\\OUQ',
	    'PoVJhJ Rf[l[ RRHR[ R[DlDl[ RTCXG R_G_I RaUgX R]KYOcO]RWS RgPdS]WVX',
	    'PoZNdN RWWgW Rf[l[ RRIR[ RWIWW R_K_U RWIgIgW R[ElEl[ R_NcR RSDVG R_NZT',
	    'PoXQfQ RXUfU Rf[l[ RRHR[ RXLXU RVMfMfU R`M_YhXhY R[DlDl[ RTDXG R]GVM R[IdI_N',
	    'PoXQfQ Rh[l[ RRIR[ RXLXQ RXLfLfQ RbQbXhWhX R[ElEl[ RSDVG RZH]K R\\UXX RdH`L R]QZW',
	    'PoYMeM RYQeQ Rf[l[ RRHR[ RYJYY RYJeJeQ R[DlDl[ RTDXG R_G`I R]SfW RfSbU R_WXX',
	    'PoXJdJ RVNiN RZUfU RZYfY Rf[l[ RRJR[ R[Q[Z R^G^N RXRfRfY R[ElEl[ RTDXG RgHcLUS',
	    'PoVKhK RWS^S Rf[l[ RRHR[ RWNWS RWN^N^S R[DlDl[ RTCXG RcHfI ReR^Y RfNcU RiUhYfY RaHcV R^VUW',
	    'PoVJhJ RXReR RXVeV R_YhY Rf[l[ RRJR[ RYNYV R_M_Y RVOfNfU R[ElEl[ RTCWG RhVhY RaJeNiO R^HYNTO',
	    'PoYLeL RYOeO RXTgT RXWgW Rf[l[ RRIR[ RXRXW RYJYO RYJeJeO RXRgRfW R[ElEl[ RSDVG',
	    'PoXM]M R`MfM RXQfQ RbXhX Rh[l[ RRIR[ RXJXQ RbQbX R`JfJfQ R[ElEl[ RSDVG R\\Q\\UYWUX RhUhX R^IWJ',
	    'PoWJ`J Rf[l[ RRIR[ RWNWQ RWNhNhQ R[ElEl[ RSDWG RYKZL R^K_L ReKbN R[TeX RYQeQaUYX RYTcV ReH_I',
	    'PoXLgL RYUeU RYXeX Rf[l[ RRHR[ RXIXP RYRYY RYReReY R[DlDl[ RTCXG R_HaMbOfP R_OWP ReGYI',
	    'PoXT^T RaTfT RXXfX Rf[l[ RRIR[ RYPYY RaPfPfY R[ElEl[ RTDWG R\\KeKbN R]HXN R]OXP',
	    'PoUL^L Rf[l[ RRIR[ R[DlDl[ RTDWG RbQfR RdJiO RdJ_O RaUgX RXQ]P]XYX RZHYLWW',
	    'PoWOgO RWSgS RUViV RRIR[ RWLWR R_L_Z RWLgLgR RSDVG RZH\\J RdH`L R[ElEl[h[',
	    'PoWJhJ RXSfS Rf[l[ RRHR[ RXMXS R_H_Z RXMfMfS R[DlDl[ RTDWG RcUfY R^ScU RcObQ R[O\\Q R_R[WVY',
	    'PoXKfK RXMfM RXPfP RVTiT Rh[l[ RRIR[ RXIXP RXIfIfP R[ElEl[ RSDVG RcReR R_UgX R_T\\WWY',
	    'PoXLgL RUOiO Rf[l[ RRHR[ RXIXL RYQYV R_G_N RYQfQfV RXIgIgL RZDlDl[ RTDWG RaVhX R^WVY',
	    'Po[NiN R]XgX Rf[l[ RRIR[ R]R]Y RcJcR R]RgRgW R[ElEl[ RTDWG RWIZJ RYTUX RUNYP RgI^J',
	    'PoXI^I RZOdO RVShS Rf[l[ RRHR[ R[DlDl[ RTCWF RgKeM R]JXN RdHaJdMiO R^TgX RWKZMUO R_S\\VVY R_O]U',
	    'PoXJfJ RVMhM RVXhX Rf[l[ RRHR[ RXSXX R\\S]X RaSaX RXSfSfX R[DlDl[ RTDWG RWQdPgR R_G_MWP',
	    'PoVIhI RXNfN RXQfQ RXTfT RUVjV Rf[l[ RRJR[ RXKXV RXKfKfV R[ElEl[ RTDWF R_G^K RbXiY R]XUZ',
	    'PoXKfK RXNfN RZZ]Z Rh[l[ RRIR[ RXHXN RVQ]Q]Z RXHfHfN R[ElEl[ RSDWG RaTdT RVSZT R]VUW RhU_X R`QhQhZdZ',
	    'PoUL_L RVS^S Rf[l[ RRIR[ RVOVS R^O^S R[ElEl[ RdSiX RSDVG RZLZUUZ RcU_Z RWIXK RbLiLgQ R]H[L RcHaO RZOYW RdNcV',
	    'PoWO]O RWS]S Rf[l[ RRHR[ RWLWW RUL^L]Y R[DlDl[ RTDWG RbPiW ReT`Y RWI]I[L RcH_P RbLhLeU R_VUW',
	    'PoWKgK RUNiN RXSfS Rf[l[ RRHR[ RWHWK RXPXS R[T[Y R\\H\\K RaHbK RXPfPfS RWHgHgK R[DlDl[ RTDWF R`UhY RhTcV R`SUV RaXYY',
	    'PoVJgJ RVRhR RRVlV RVFVR R_R_[ RVFgFgJ RVNhNhR R^C\\F',
	    'PoVV^V RRDR[ R[N_R_U RRE`EXL^O',
	    'Poe[j[ RRDR[ R`UZ[ RVLYNZR RREZEVL RbP^X RYTUT R[JlJlTj[ RcCcKaT',
	    'PoRDR[ RfUl[ R`U[[ RXLZQ RcCcNhX RREZEVK RbP^X RYTVT RcJdS RcIaU',
	    'Po[NeN RRDR[ R`E`[ RlWlZ RREYEVK RWLYQYTUT R[EhEhQiZ',
	    'Po[NlN RRDR[ RdEd[ RREZEVK RXLZPYTVT RiD^F',
	    'Po[FlF Rf[l[ RRDR[ RgHg[ RWMYR R]VY[ RlWl[ RREYEUL RYUUV R_H_P^V',
	    'Po^HlH R][k[ RRDR[ RdQ\\Y RfObS R^MiMdQ RlWk[ RWLZQ RREZEWL R`C\\L RYTVT',
	    'Po^[k[ RRDR[ R^F^Z RdCdU RWLYQ RlVkZ RREYEVK RkHZM RXTUT RkHkSgT',
	    'Po^[b[ RRER[ RbDb[ RgSlX R]SXX RkIeO RbIhU R_N[V RREXEVLXPXT RbFeQ RYK_K]T RXUUU',
	    'PoRDR[ ReUk[ RWMZR RdDcPgX RkHhO RREZEVL RcO`V\\YY[ R]H[O RYUUU RcKdT',
	    'PoRDR[ RgCg[ RWLYQ R]McO R_FdH RREYEVK RYTVT RcT\\U RgT_U RkScT',
	    'Po\\ElE RfSjS Ra[k[ RRDR[ RaJaZ RaJjJjS RlWk[ RWLYQ RREYEVL RXTUT R\\E\\Q[X',
	    'Po[NlN RRDR[ RgDg[ RWLYQ R^VY[ RREYEVK RdD[G RXTUT R_F_Q^V',
	    'Po]ElE RRDR[ RULYT R_McTgYl[ RREYEVK R]MjMgTcX][ RXTUT R]E]P[X',
	    'Po\\FlF RRDR[ RdHd[ RWLYQ RfLlQ RcJ[R RREYEVK RdFbL RXTUT',
	    'Po[HlH Rh[l[ RRDR[ RcCcG R^MhMhZ R]WZ[ RlVl[ RREYEVK RXMZPYTUT R^M^S]X',
	    'PocLjL R[McM Rf[l[ RRDR[ RfOf[ RWLYQ R_I[L RfGiJkN RlWl[ RREYEVK RaC^J R^V[ZX[ RgLaL RXTUT R_O^W',
	    'Po]EjE R[MlM Rf[k[ RRDR[ RfMf[ RWLYQ R^WZ[ RlWk[ RREYEVK RXTUT R`M_W',
	    'PodMlM RYZlZ RRDR[ R^I^Z RdDdZ RXMZR RREZEVL RYTUT',
	    'Po\\LbL ReZlZ RRDR[ R\\D\\Z ReCeZ RWLYQ RlWkZ RREYDVK RkKdN RYTUT RbX[Z',
	    'Po[IlI R[QlQ RRDR[ RhCh[ R^WZ[ RREYEVL RWMYRXUUU R_C_Q^W',
	    'PoZHlH Rc[h[ RRDR[ RcCcH RVLYT RREZEVL RYUUU R`NjNjUi[ RaH_R]WY[',
	    'Po_NlN R_YlY RRDR[ R_E_[ R_ElEl[ RREZEXHVKZPZTUT',
	    'Po`KlK R_RlR Rg[l[ RRDR[ R`ElEl[ RRE[EWK RYL[P[TVT R`E`R^X',
	    'Po[GlG R]PkP R[VlV RRDR[ RdKd[ RWLYQ RREYEVK RaD^N RXTUT',
	    'PoRDR[ RgNg[ RcEjL RbF[M RREYEUK RbC`H R\\ZZ[ RWMYQXTTT R_N_S^X',
	    'Po\\ElE RcOkO RYZlZ RRDR[ R]L]Z RcEcZ RWMYR RREYEVL RXUUU',
	    'Po\\FcF R\\MkM R_QjQ R_ZlZ RRDR[ R\\IkIkM RlWlZ RWLYR RcU_Y RREYEVL RiRcU RYUUU R\\E\\Q[Y RjDaE',
	    'Po[IkI RZOlO RcYkY R_YiY R]ZfZ RZZdZ RRDR[ RjWk[ RgTjX RREYEVL RcCbO]Z RWMYQYT RXUUV',
	    'Po\\MlM RZZfZ RRDR[ R\\F\\W RWLYQ RREYEVK RXTUT RlVkZiY RdUZW ReEeLgW RiD\\F',
	    'Po]LiL R]SiS RYZlZ RRDR[ R]E]Z R]EiEiZ RREYEVL RWMYQYTUT',
	    'Po^HlH RbMkM RbTlT RRDR[ RbHb[ RREZEVK R`C\\L RYTUT RXMYQYT',
	    'PocIlI R]ZjZ RRDR[ R]P][ RcCcP R]PjPj[ RREZEWJ RVKZOZTUT',
	    'PoRDR[ R_Ue[ RWLYQ RaKdM RcDkL R\\PjPbX RcE[M RREYEVL RXTUT',
	    'Po[ElE R\\SdS RUUXU Rc[i[ RRDR[ R\\K\\V R\\KdKdS RiEi[ RWLYQ RREYEVK',
	    'Po_[k[ RRDR[ R[G[K R`L`Z RcCcG R[GlGlK RWLYQ RlWk[ RREYEVK RXTUT RjN_R',
	    'Po^HlH R_[k[ RRDR[ R_K_[ RdIdV RWLYQ RlXk[ R^GZK RREYEVK R_C\\J RjL[Q RXTUT RjLjUgU',
	    'PoUTXT RRDR[ RcCcN RWLYQ R^N`SeXl[ R\\HkHjL RREYEVK R\\NjNgScX\\[ R\\H\\QZX',
	    'Po\\ElE Rg[k[ RRDR[ R]J][ R]JkJk[ RWLYQ RdEdNhS RbP^U RREYEVL RXTUT RdHcQ',
	    'Po`JlJ Rd[i[ RRDR[ R^H^[ RiCi[ RbNdT R_C[M RREXEVLXPXT RXUUU',
	    'Po]FjF R\\LlL RUUYU R_[c[ RRDR[ RdLd[ RWLZQ RhQlX RREYEVK R_Q[X',
	    'Po]HjH R[NlN R]ZkZ RRDR[ R]R]Y RdCdY RkRk[ RULXNYR RREYEUL RXTTT',
	    'Po[JlJ Rd[l[ RRDR[ ReJeZ RlXl[ R]VX[ RiP`Y ReEiF RjNhR RREZEWK R_Q[Y RXLZQYTVT R`C`K^U',
	    'PoZGlG R[PjP R^[c[ RRDR[ RcKc[ RiTkY RaD\\P R]T[Y RREXEVKXOXTUT',
	    'Po]QkQ R[ZlZ RRDR[ RcQcZ R\\EiEcKlN ReJ[N RXTUT RREYEULYOYS',
	    'PobPlP RRDR[ RWLYQ RREYDVK RcC_H[J RaJeL\\P R`GiGdMaQ[T RkRfW^Z RYTUT RbTeWZ[',
	    'Po]ElE RgWjW R[ZlZ RRDR[ R[I[[ R_I_V R_IkIkV RVKXQ RdIhR RREXEVL ReJbQ RXTUT RdEeJcO',
	    'Po\\ElE R]RjR RTUXU R]YjY RRDR[ R]K][ R]KjKj[ RWLYR RREYEVL RcEbK',
	    'Po\\QjQ RZWkW RRDR[ R_Q_W ReNe[ RULXT RbC[J R_GdLkO RREYEVK R_FiFdL[O RXTUT',
	    'Po^IjI R[PkP RUUXU RRDR[ RdCd[ RWLYQ RfSlY RaS[Z RdNfT RREYEVK RbP`U RdNbS R^E\\M',
	    'PoZGlG R^PjP R^UjU Rf[j[ RRDR[ R_J_[ R]MjLj[ RWLYQ R_JZO RREYEVK RbC]M RXTUT',
	    'Po]JjJ R]OjO RRDR[ R]D]Z R]DjDjO RREZEVK RjReT RVLYQYTUT ReYYZ RcPeUk[',
	    'Po\\ElE Rh[k[ RRDR[ R\\K\\[ RaKaZ RfKfZ R\\KlKl[ RVLXQ RREYEUL RdEaL RXTTT',
	    'Po\\KlK ReUiU Ra[k[ RRDR[ RbNbZ RaNiNiU RWLYQ RkWk[ R^HZL RREYEVK R_FfFdJ RXTUT R_C]J]S[Y',
	    'Po[GkG R_MhM R\\QjQ Rb[g[ RRDR[ RWLYQ ReGkM RbG[N RREYEVL RcC`J R`Q_UiUg[ RXTUT',
	    'PoZGlG RRDR[ RgWl[ RdUhW RWLYQ RgJ_R RbFaI]M RREYEVK R[L]MaQ[T RjPcVY[ RXTUT RcCcG',
	    'Po\\HkH R[QlQ RRDR[ ReUk[ RWMYR R^J_O RcCcQfW RiJgO RREYEUL RbU^YY[ RXTTT RcLbU',
	    'Po\\IkI RRDR[ RfIeOkNkO RWLYQ RgFkJ RaP[V R_ScWj[ RREYEVK RaC_H R`I_N[O RXTUT R_ShSdW`YZ[',
	    'Po]PjP R]UjU Rf[i[ RRDR[ R]K][ RcCcK R]KjKj[ RgElJ R^FZJ RREYEVL RXUUU RWMYRYU',
	    'Po[ElE R[SkS RYZlZ RRDR[ RcScZ R[L_P RaLeP RfLkP RjGfK R_G[K RdG`K RVKXQ RREXEVL RXTUT',
	    'Po\\EkE R^JjJ R\\NlN RRDR[ R`N`[ RgVlZ RWLYQ RjQgS ReOhX RREYEVK RXTUT ReY^Z R\\E\\RZ[',
	    'Po]FdF R[JlJ RaQeQ RRDR[ R]N]Z RaLaY RWLYQ RhDkG RREYEVK RlWj[k[ RXTUT RgCfNhZ ReY[Z',
	    'Po\\HbH R[TjT RYZlZ RRDR[ R\\D\\O RbPbZ ReDdNkNkL RVKWT RREXEVL RkGdH RXTUT RbMYO',
	    'PoZHlH RRDR[ R]M`Q RhNlR RVKXQ RbDbQeVhYl[ RhKfQ RREXEVL R]K[R RbP`V]YY[ RXTUT RbLcU',
	    'PoZHlH RRDR[ RiOlR RVKXQ RcDcQeVhYl[ RhMfR RREXEVL R]L[S RcP`V]YY[ R]K_R RfKhKjQ RXTUT RcLcT R]K[K',
	    'PoZJlJ R[TjT RY[l[ RRDR[ R_E_M RcPcZ RgCgP RWLYQ RREYEVK RXTUT RcD[E',
	    'PodGkG RYLlL R`UdU RRDR[ R^F^L RdCdU RWLYQ RREYEVK R^O[T RXTUT RjOiTeXbY[[',
	    'PoZHlH R[QkQ R[UkU Rg[k[ RRDR[ R[L[[ RcCc[ R[LkLk[ RWLYQ RfDkF RREYEVK RYTUT',
	    'Po[GjG RZMlM RcSjS Rc[l[ RRDR[ RWLYR R]S_WbZ RREYEVK R^P[Y RYUUU RcCbZiZ',
	    'Po]KiK R[PlP Rf[l[ RRDR[ RcCcG RfPfZ R[GlGlJ RlWk[ RVLYT RREYEVK RXTUT R[G[K R`P^W[[',
	    'PoZFlF R[NkN R[SkS RYWlW RRDR[ R[J[R RcCc[ R[JkJkR RVLXT RREYEVL RXTUT',
	    'Po]KhK R[QkQ R_[b[ RRDR[ RcKc[ RVLXT RgTkW RcDiJlK RcD[J RREYEVK R]S[X RXTUT',
	    'PoaSlS RaZlZ RRDR[ R]M][ RaMa[ RaMlMl[ RWLXT RREYEVL R`FiFgL RXTUT RaD_H[K',
	    'Po[FkF R\\JkJ RZNlN R[RlR Rc[h[ RRDR[ RhNh[ R`TcX RWLYQ R^TXY RREYEVK R`M\\W RaI^T RXTUT RbC_R',
	    'Po\\IjI R\\NjN R\\TkT RYZlZ RRDR[ R\\D\\N RcPcZ R\\DjDjN RWLYR RREYEVL RXTUT',
	    'Po]IiI RRDR[ R\\M\\W R]D]I R]DiDiI R\\MjMjW RWLYQ ReWl[ RREYEVL RcV`Y[[ RXTUT RcPbW',
	    'Po^LgL RTUXU RZZlZ RRDR[ RVLXQ RcDjL RbE[L RjOeZ RREXEUK R]P^V RbOcU',
	    'Po[GkG RZNlN R]ZjZ RRDR[ R]R][ RcCcG R]RjRj[ RWLXS R^H_M RREYEVK RhHgM RXTUT',
	    'Po[HaH RfHlH R[NaN RfNlN R[SaS RfTlT RRDR[ RaCa[ RfCf[ RWLYQ RREYEVK RXTUT',
	    'PoYEkE R[PaP RRDR[ R[E[W RaEa[ RhWl[ RgWc[ RePiY RjPeY RUKWOXS RdKgV RREXEUL RWTUT R[JaJkIhV RaVXW',
	    'Po\\GkG R[OlO R]WdW Re[i[ RRDR[ R]R]Y R]RdRdW RiOi[ RWLYQ RcIkL RREYEVK RcHaK\\M RdCbJ RXTUT',
	    'Po]IlI R]NkN R]TkT R]YlY RRDR[ R^H^[ ReGeY RWLYR RREYEUL R_C[L RfDdJ RXUUU',
	    'Po^KhK R[TjT Rh[j[ RRDR[ R[O[[ R`O`[ ReOe[ R[OjOk[ RWLYQ RcC\\K RcDgHlJ RREYEVL RXTUT',
	    'Po^IgI R\\QjQ RZTlT R^ZjZ RYZeZ RRDR[ RfVk[ RbCfGlJ R\\LgLfP RREYEVK R_T]Z RbC]HXJ RULYS RXTUT',
	    'Po[QhQ Re[j[ RRDR[ RbJbZ RdSgV RaQeT RWLYQ RbQZX R]K^O RgKeO RREYEVK R_C[K RXTUT R]HlHlSj[',
	    'PoZJkJ RYOlO RZUlU RYZlZ RRDR[ R]J]T R[EcEcZ RhJhT RWLYQ RREYEVK RXTUT RiDbE',
	    'PoZFkF R\\NjN R\\RjR RRDR[ R\\J\\R RcCc[ R\\JjJjR RVLXT RcSiXlY RREYEVK RcS]XYY RXTUT',
	    'Po\\KjK R\\PjP RZUlU RRDR[ R\\F\\O ReQe[ R\\FjFjO RUKXR RREXEVL RbCbFcO`R\\S RXUUU',
	    'Po\\FjF RZKlK RRDR[ RcCcK RfKfPkPkO R`PZV R_M\\P RULXT RREXEVL R^SbWfYl[ RXTUT R_SiSeWaYY[',
	    'Po^LhL R[PhP R\\VgV Re[j[ RRDR[ R\\R\\X RbLbV RgRgX RWLYS RREYEVK R_I\\N RYTUT R\\HlHlTk[ R^D\\HYK',
	    'PofLjL R]TcT RfTlT R]ZlZ RRDR[ RXNYS R]O][ RfOlOl[ R`G[L RREYEVK RaC^J RcN]O RaFlFjL RYTUT',
	    'Po\\FjF RZKlK R[UjU RY[l[ RRDR[ RcCcK RcQcZ RfKfPkPlN R`K^PZQ RREYEULYSUT',
	    'PoZJlJ R\\UiU R\\ZiZ RRDR[ R\\Q\\[ RgCgO R\\QiQi[ RWLYQ RREYEVK R^O\\O RXTUT RcD[E R`F_N',
	    'Po]JiJ R\\TjT RRDR[ R\\N\\T R\\NjNjT RcJcTdVgYl[ RcCgGlI RREYEUL RbU_YY[ RbC^GZI RWMYQYTUT',
	    'Po[ElE R[OkO R\\TjT RYZlZ RRDR[ R[I[O R`E`O RcPcZ RfEfO R[IkIkO RVLYR RREYEUL RXUUV',
	    'Po[GfG RYLlL R^UiU R^ZiZ RRDR[ R_P_[ RaCaL R]QiQi[ ReNhO RREYEUL RiEhHbN_PZR RXNYT RXUUU',
	    'Po]HiH R]LiL RZOlO RRDR[ R]D]L R]DiDiL RWLXT R^QYV RaT[Z RREYEVK RXTUT Ri[f[ R]SkSi[ RfSdWa[',
	    'Po[ElE RhPjP R[TlT RRDR[ R\\I\\P RaIaP ReIeP R\\IjIjP RcSdWgYl[ RcE`J RbV_YZ[ RcQaX RREXEULXRXUUV',
	    'PoZEkE R\\JkJ RZYlY RRER[ RZEZ[ R_NbP RdRiV RhNkQ RcR^W RULXQ RdGdPfU RhLfQ R`L]Q RdOaU RREXEUL RWUTU',
	    'Po[HlH R\\OjO R\\SjS RUUXU R[WkW RZ[l[ RRDR[ R\\L\\S R\\EcEcZ R\\LjLjS RWLYQ RREYEVK RjDbE',
	    'Po[ElE R_KiK R_NiN R]QlQ R[ZlZ RRER[ R[E[[ R_H_N R_HiHiN RcN`TjW RREXEULXQXUTU RhReV^X',
	    'Po]JhJ R]MhM R]QhQ RZTlT R_ZjZ R]ZgZ RZ[d[ RRDR[ RfVk[ RVLYR RcCgGlI RREYEUL R]J^T\\Z RbC^HYJ RXUUU',
	    'PoRDR[ RZOZ[ R\\Q_S R\\U_W R\\EbDiJ R\\JXL RbD[K RcTf[ RhTiWk[ R^KgJlK RfNcU RkNhU RREWEULWPWS RWTTU RZOaOa[^[',
	    'Po]IiI R]MiM R[PlP RcUjU RbZlZ RRDR[ R]E]L RdPdZ R]EiEiL RWLYQ RREYEVK R]SZZ RXTUT R]U`YgZ',
	    'Po]IkI R]MkM R^WiV Rh[l[ RRDR[ R[Q[[ R]D]M RdDdV R]DkDkM R[QlQl[ RWLXS RgSiX RREYEVL RYTUT',
	    'Po]RjR R]VjV RZZlZ RRDR[ RcOcZ RWLXS RaCZJ R_GcLkO RREYEVK R^O[T R_FiFdK[O RXTUT',
	    'Po[GkG R]KdK RRDR[ RlXl[ RhDjE RbU]Z R^UcW RjOeZ RjKgW R`K^T R\\OdObW RREYEVLXQXTUT RgCfMhY R[G[SY[',
	    'Po\\IjI R\\NjN RZQlQ RRDR[ R\\D\\M R]Q]Z RcDcM R\\DjDjM RWLYQ RcQeVhYl[ RREYEVK RkTeV RXTUT ReYZZ',
	    'Po\\JaJ ReJkJ R\\OkO R\\TkT RZZlZ RRDR[ R\\E\\O RcPcZ ReEkEkO RWLYR RREYEVL RXUUU RbD\\E',
	    'PoZHlH RRDR[ RWLXS RkLgP RhCfG R]C_G RREYEVL RbH`KZN RcR_UXY RXTUT R_KbOdWc[`[ RcIgSlW RbO_Q[R',
	    'Po[FlF R`IjI R]MlM R^SjS R^WjW Rf[j[ RRDR[ R^P^[ R^PjPj[ RWLYQ R`H[M RREYEVK RaC^J RdIeM RXTUT',
	    'Po\\GkG R[MlM R]UjU R]ZjZ RRDR[ R]Q][ RcCdG R]QjQj[ RWLYQ R^H`L RhHfM RREYEVK RXTUT',
	    'Po\\JjJ R\\NjN R[QkQ R\\VkV RZ[l[ RRDR[ R\\F\\N RbCaF RcQcZ R\\FjFjN RWLYQ RREYEVK RXTUT',
	    'Po\\GbG RdNlN R\\UjU R\\ZjZ RRDR[ R\\C\\N R\\Q\\[ ReDeM R\\QjQj[ RlKlN RbNaQ RVKXQ RREXDVL RkFdI RXTUT RbLZM',
	    'Po^GlG RbNjN RbSjS RgXjX R`[l[ RRDR[ RbIbX RaKjKjW RYN]N]W`Z RZE]H R\\VX[ RbI^M RUKXQ RREXDUL RdC`L RWTUT',
	    'Po]NjN R\\RjR R^[g[ RRDR[ R_U_[ RgXg[ R\\JjJjR RWLYQ RhUlY R_FgFcJ RaD[I R`TeV RREYEVK R[VZZ RYTUT',
	    'Po\\IjI R[LlL R]TjT RZ[l[ RRDR[ R\\D\\I R]O]T RcCcI R]OjOjT RjDjI RVLYR R_V`Y RgVfY RREYEUL RXUUU',
	    'PoZHkH RZOlO R[SjS RRDR[ R_H_[ ReGe[ R[KjKjS RdSkZ R]D_F R_R^VYZ RgDdH RREXEVKXQXTUT',
	    'Po[FlF R_JjJ R]NlN R[RlR R_UjU R]ZlZ RRER[ RWLYQ R`SYY R_HZM RREYEVK RaC^J R`O^U RdJeN RdUeZ RXTUT',
	    'Po[ElE R]LjL ReSlS R_VhV Rh[l[ RRDR[ R[O[[ R]H]L RcVc[ RfOfS R]HjHjL R[OlOl[ RaO`S]T RREXEVKXOXTUT',
	    'Po]IiI R\\OjO R\\SjS R\\WjW RRDR[ R\\L\\W R]D]I R]DiDiI R\\LjLjW RVKXQ RgXl[ RREXEVL R_XY[ RXTUT',
	    'Po\\KiK R\\OlO Re[j[ RRDR[ R_U_Y R\\FiFiK RfUhW RcUdX RWLYQ RbCaF RREYEVK R[VY[ RXTUT R\\F\\SkSj[',
	    'Po\\KiK R\\PiP RdWkW Rc[l[ RRDR[ R\\F\\P RcFcZ R\\FjFjP RWLYQ R]WY[ RbCbF RjTkW RREYEVL RfRfW R_Q\\X RXTUT',
	    'Po^IgI RY[l[ RRDR[ R\\S\\Z RaSaZ ReSeZ R\\SiSiZ RWLYQ RaMgNlS RaEfEkJ R^FZJ R^OZS RREYEVK RXTUT',
	    'Po\\NjN R\\RjR R_[c[ RRDR[ R\\K\\R RcCcK RcRc[ R\\KjKjR RWLXS RhUlY RgEkI R^UZY R_E[I RREYEVK RXTUT',
	    'Po]QiQ R[UjU RY[l[ RRDR[ R]L]Q RcCcH RcQcZ R]LiLiQ RVLYR R\\D]G RjDgF RREYEUL RXUUU RZHZL RZHlHlL',
	    'Po]OgO R[SjS R^[b[ RRDR[ RbSb[ R[J]L RgUkY ReJkP R]UYY RVLWS R[G_J RREXEUL R\\CZI RWTUT R\\FaF_KYP ReFkFfN',
	    'Po[FkF RYJlJ R\\PiP R\\SiS RYWlW RRDR[ R\\M\\S R^F_J RcCcF RcSc[ R\\MiMiS RWLYS RREYEVK RgFeJ RXTUT',
	    'Po\\IiI R\\MiM R\\PlP Re[j[ RRDR[ R_U_Y RbCbF R\\FiFiL RfUhW RbUdY RWLYQ RREYEVK R[UYZ RXTUT R\\F\\SkSj[',
	    'PoXGbG ReIlI RYKaK RXObO RRDR[ RgVl[ RgVb[ ReQiY RVTTT R`[\\[ RREVEUKVS RfCcLgV R]D\\OZY R[TaS`[ RjJhV',
	    'Po\\MjM R\\QjQ RZTlT RRER[ R\\J\\Q RcJc[ R\\JjJjQ RWMYR R[F^H RaFdH RhFjI RjChF R]C[F RcCaF RcSgXlZ RREYEUL RcS_WYZ RXUUU',
	    'Po\\OjO R[RjR R_[g[ RRDR[ R`U`[ RgXg[ R[KjKjR RWLYS RhUlY RiFhJ R\\G]J R`TeU RREYEVK R[VYZ RbFbJ RYTUT RiDcE\\E',
	    'Po[FkF R[KlK R^QkQ R^UjU R\\ZlZ RRDR[ RdMdZ RVLYQ RcCcF R_G_J RREXEUK R_N]S RgGeK RXTUT R[K[RYY',
	    'Po]FiF R[IkI R[MbM RdMkM RcQlQ RZTlT Re[l[ RRDR[ R^K^Q RcCcI RfTf[ RgKgQ RlXl[ RREYEUL RaT^YZ[ RXNXS RXTUT RbPZQ',
	    'PoZHkH RbQlQ RaWlW RRDR[ RbCbN RdQdW RhNh[ R\\D^F RiDfG RVKWS RbGeJlM RREXEVL R]NZT RbG_KYM RXTUT R[S_V R\\QaQ_WZ[',
	    'Po\\IjI RZKlK R]QjQ R]TjT R]WjW RRDR[ R\\E\\I R]N]W RcCcK R]NjNjW R\\EjEjI RWLYQ RfYlZ RREYEVK RXTUT R`YY[',
	    'Po^LgL R\\TiT RY[k[ RRDR[ R\\O\\T R\\OiOiT R[G]J ReGjM RhDfG RkGhJ R`GZM RVKXQ R_U_Y RdCfI RfUeY RREXEVL R[EaE^I RXTUT',
	    'PobGlG RZL`L RbNlN RbTlT R[YaY RRER[ RZE[R[[ RZE`E`L R[QaQaY RdIeM RgCgG RgNg[ RiIiM RREWEUKWPWTTT RZNXY',
	    'PoaIiI R`[l[ RRDR[ RgOgW RZJ]M R[D]G RcLkT RdEhElI RYP\\P\\WX[ RaE_I RUKXQ RREXEUL RdIbM^N RcO^R RjLfPcT^V RWTUT RfXbX R^Ze[',
	    'Po^FkF RcIjI RaLlL RaRjR RaTjT RgXjX R`[l[ RRDR[ RaOaX RfIfL RaOjOjX RYJ\\L RZD]G RYQ]Q]WX[ RUKXQ RREWDUL RcC_K RWTTT',
	    'Po^MiM RZUkU RRDR[ R[F[R RdHdQ R[FkFkR RWLYQ RcL^Q RfIfK RbCaF RaMgO RcTdVfYl[ RREYEVK RXTUT RcSaW_YY[ RgH`H',
	    'Po[RaR RdRjR RRDR[ R[M[R R[MaMaR RdMdR RdMjMjR RhWk[ RfViY R]V`Y RcChI R]VY[ RfWb[ RVKXQ R^JgIlJ RfTdZ RREXEVL RbC\\IXJ RXTUT',
	    'Po[FkF R[JlJ R\\PjP R\\SjS R\\WjW RRDR[ R\\M\\W R_H_M RcCcH RgHgM R\\MjMjW RWLYQ RfXl[ RREYEVK R`X[[ RXTUT',
	    'Po[DlD Rg[j[ RRDR[ R\\I\\[ RcDc[ R[KkJk[ R^MaQ ReTiX RVLXQ ReNiQ RgFlJ RaM]Q R^TaV]Y RhNdQ RhSgVdY R_FZJ RREXEUK RWTTT',
	    'Po\\EkE R]JaJ RfJiJ R\\MaM RfMjM R[PlP Ri[k[ RRDR[ R[H[K R\\T\\[ RbSb[ RcEcN RfTf[ R\\TkTk[ R[HlHlK RWLYQ RREYEVK RYTUT RbPaW',
	    'PoZFlF R\\WiW RRDR[ RcCbF RcJcO RfJfN R\\RiQi[ R`HaI ReGdI RUKWQ RiIkO RREWEUL R\\JZN RkIfI RZJ_J_O]O RiNeN RWTTT R\\R[Y',
	    'Po[EkE R\\HjH R\\MiM R[OjO RZRlR RbUlU R[Y`Y Rf[i[ RRDR[ R[T[[ R[T`T`Y RcMcO RcCcH RiSi[ RZKlJkM RcWeY RVKXQ RREXEVL RXTUT',
	    'Po[HjH R[KjK RZUaU RcUkU RRDR[ R[E[K R_W`[ R[EjEjK R`SbU RZO]R RjXk[ RdOgR RiSlU R`P[U RgKeP RiPdU R^K\\P RVKXQ RREXEVL R[WY[ RdWe[ RXTUT',
	    'Po\\IiI RZLkL RZQlQ R\\TiT RRDR[ RbIbL R^[gZg[ R\\NiNiT RhVlZ R]EbEbH RhEkI R[WYZ RVKXQ RaUdW RREXEVL R\\DZI RXTUT R^W^[ RiCaD',
	    'Po_IcI RZMkM R]OjO R]RjR RRDR[ R_W_[ RcDcI RdLdU RfWhX RWLYQ RhElH R^EZH RcWdZ RREYEVK R[WY[ RhHaKZM RXTUT Rg[j[g[ R^L]UkUj[',
	    'Po[EkE RaGiG R_IkI R^OiO RfRiR RZ[][ RTETR R]T][ RdGdI R^KiKiR ReViZ RWIZL RYVUZ RTEZEXI R`E[J RaVbZ RZOVO R_K_RkU R^MbM^M R^SRV RaMdM RfMiMfMcM',
	    'PodFlF RYFbF RXLbL RdMkM RdPkP RZRaR RdSjS RZVaV RdWkW Rd[l[ R^[a[ RRDR[ RZOZ[ R[H[K R]C]F RZOaOa[ ReMe[ RVLWR R`H`K RdCdIkIkN RREWEUL RWTTT',
	    'Po^EcE RfLlL R^NbN R[PdP R[UcU RRDR[ R[R\\U R^I^M R]W]Y R^C^H R[RcRcU RYHcHcK RlWk[ RhEkH RaW`Y RREWDTKVOWTST RjNfXcZZZ RgCfNhZ RYHYRXY RaK[K RiKdL',
	    'PoRJlJ RUOiO RZ[_[ R_C_[ RUFiFiO R_NaSeXlZ RUQYS RiRfScT R[WRY',
	    'PoSF]F R^JlJ RSJ\\J RSN\\N R`OjO RRR]R RU[X[ Ra[d[ RXCXJ RXRX[ ReCe[ R`FjFjO R`QaT RfTkY RkQhT R[T]W RdOhV RTTRY RbW]Y',
	    'PoRG]G R^JlJ RSN\\N R_OjO RRR]R RXCXL RdCd[ R_FjFjO RYI\\L RWGZJ R`QaT R[T\\W RfTkY RkQhT RXGRL RdOgV RTTRY RbV]Y RXRX[T[ Ra[d[a[',
	    'PoWJkJ RWNjN RWTjT RWYlY RWHW[ R`G`Y RWJRN RYCUL RaD_I',
	    'PoWMkM RWQjQ RWUjU RWZlZ RTGTJ RXLX[ RaLaZ RTGlGlJ RcIaM R]C\\HTORO',
	    'PoVGjG RVJhJ RVMhM RVQjQ RSThT RWFWQ R_G_Q RWGRK RXCVH R_C^G RYU_XgZ RfUaXXZ R^Xk[ RbXT[',
	    'PoVGiG RVJhJ RVNhN RVRkR RRUlU RWFWR R`E`[ RXCWGRK',
	    'PoWGkG RWJiJ RWMiM RWPlP Rf[j[ RXEXP RaEaP RXERK R\\S[WXYS[ RTSeSeVlVj[',
	    'Po_IlI R_NkN R_SkS R_YlY R`H`[ RfHfY RWTRX RSKVMXQZV ReDfG RZLUW RaD]M RSG[GWT',
	    'Po`IlI RaNlN RaTlT RaZlZ RaGa[ RgHgZ RXE[H ReDhF RaG]K RcCaI R]V\\[ZZ RVCVLXX RXKRL R[KUK',
	    'PoZJ_J RUOkO RXRjR RXVjV RXZlZ RXNX[ R_C_J RaOaZ RgEkJ RdLaO RXETI ReI]LSO',
	    'PoUElE R_LkL R_PjP R_UjU R_ZlZ R[J[[ R`J`[ RfJfZ RZLVP R[GXO RaG]O RfGeN RUEUQSZ',
	    'PoaIlI RaNlN RaTlT RaZlZ RaGa[ RgHgZ RXKYN RXE\\I RfDhF RWFRK RXCVH RbD^M RSQ\\QXZ RbCaL',
	    'PoSE^E RaIlI RRN_N RaNkN RaTlT RaZlZ R[E[[ RbGb[ RgIgY RfDgG RcC_K RVEVQUX',
	    'PoSH_H R`IlI RaNkN RaTkT RaZlZ RaGa[ RgGgZ RbC^M RgDfJ RWCWNSY RZLXY R\\S^YWY',
	    'Po`IlI RRN^N R`NkN R`TkT R`ZlZ RV[[[ RUEUM RSE\\E[[ RaGa[ RfGfZ RXRRX R[LXS RbC^M RgDeH',
	    'PoVGiG RVJhJ RVMiM RVQkQ RSTlT RWFWQ R`E`[ RWFRK RaUlZ RXCVH R`C_G R_S\\VR[',
	    'PoSElE RULjL RZPkP RZSjS RZVjV RZZlZ RZOZ[ RcNcZ RUHjHjL R[NVS R\\LZQ RcLbP RUHUQTX',
	    'PoSF[F RcFlF RVMjM RWQiQ RWUiU RWYlY RWLW[ R[C[H RaKaY RcCcH RWLRQ R`IaL RXIVN',
	    'PoTG\\G R_IlI R_NkN RRO]O R_TkT R_ZlZ R`G`[ RfGfZ RXGXR\\W RVVRZ RXRTY RaC]M RgDfJ RUCSL RXLWV',
	    'PoaIlI RaNkN RaTkT RaZlZ RULUV RaGa[ RgGgY RVDRM RbD^M RgDfJ R[[X[ RUH]G]T\\[ RULYLYTUT',
	    'PoVHkH RWJjJ RWMjM RWQlQ R\\XdX Rg[k[ RUSU[ RWFWQ R`F`Q RdSkSk[ RXERK RaD`G RUS\\S\\X RdSdX',
	    'PobIlI RbNlN RbTlT R\\Z_Z RbZlZ RTHTY RWCWX R\\C\\Y RcGc[ RhGhY R`I[M RdE`M RhDgJ R`V_Y RWKZKWK RZXRY',
	    'PoRFlF R_MkM R_PkP RRTXT R_UkU R_ZlZ R`K`[ R_C_G RfKfY R[KYN RWITLXOTT R_K[O RaH^M RgHeL R[RZVVYR[ R[STT',
	    'Po_IlI RTK[K R_NkN RTQ[Q R_TkT R_ZlZ RTDTX R`G`[ RfGfZ RaC]M RgDfJ RTD[D\\WRY',
	    'PoaIlI RaNkN RTS\\S RaSkS RaYlY RSY\\Y RTM\\M\\[ RaGa[ RgHgY RfDgG RWCSL RVG]FYM RbC^L',
	    'Po_EiE R^JgJ R[JdJ RXJaJ R^MlM RZQkQ RZSjS RZWjW RZZlZ R[P[[ RcOcZ RUGlGkJ R[PWS R_C^K RUGURTY',
	    'Po`HlH R`NlN R[N^N RSQ^Q R`TlT R`ZlZ RXNX[ RSG\\G\\N RaGa[ RgGgZ RYR[U RXQTX RbC_J RhCfI RWCULRN',
	    'Po`IlI RaNkN RaTkT RTZ]Z RaZlZ RTRT[ RTR]R][ RaHa[ RgGgZ RXK^Q RUHWK RWCTJ RcC^M RhDgI RVG]G[KVPRR',
	    'PoaIlI RTK\\K RaNlN RRP]P RaTlT RaZlZ RU[X[ RYKY[ RaGa[ RgIgZ RYD]H RYDRK R\\S]W RfDgG RTSRY RbD^N RbDaL',
	    'PoTI^I RaIlI RTM]M RaNkN RTS]S RaSkS RTY^Y RaYlY RUGU[ RZHZY RbGb[ RgHgY RYCZF RfCgF RUCTL RcC`IaL',
	    'PobIlI RVI\\I RVL\\L RbNlN RbTlT RVV\\V RbZlZ R[[^[ RVPVX RYFYO RVP\\P\\V RTE^E^[ RcGc[ RhGhZ RdD`M RhDgJ RTETTR[',
	    'Po`IlI RTJ\\J R`NkN RSS\\S R`TkT RVY]Y RSZYZ R`ZlZ RSNSS RTDTJ RXJXY RTD\\D\\J RSN\\N\\S RaHa[ RgGgZ R\\V][ RbC^M RhDfI',
	    'PoRG\\G R_GlG R`MlM RSN[N R`QkQ RSR[R R`UkU RRV]V R`YlY RSJSR RXCXJ RXRX[ RSJ[J[R RaLa[ RfMfY RfIhM R`D^J RaI_O',
	    'Po]ElE R^KkK R_MjM R_OjO R_RlR RWEWP R_I_R RaCaG RdIdR RgCgG R_H]L RaWfYl[ RTE[E[[X[ R^UjUgWdY][ RTETTS[ R[ORQ',
	    'PoUG^G RaGkG RUI^I RbIkI RUL^L RbLkL RUO_O RbOlO RUFUO RZEZO RbFbO RgEgO RVCSI RcC`I RTShSeUaXWZ RXR[V`XdZl[ RbXS[',
	    'PoRElE RUM]M RaMjM RVQjQ RVTiT RVWiW RVZkZ RWPW[ RZCZG R_O_Z RdCdG RaIjIjM RWORT RUIUM RUI]I]M RaIaM',
	    'PobIlI RTK\\K RbNlN RZN^N RUR_R RbTlT RTW\\W RbYlY RZ[^[ RTGTK RTSTV R\\H\\K R\\T\\W RcGc[ RhFhY RXWU[ RVERH RXKTO RVPRT RdC`M RhDgJ RXGWM RXSXY RUF_F^N R_S^[ RcDbL',
	    'PoSF^F RaIlI RaNlN RRR_R RaTlT RaZlZ RYCYG RYPY[ RbGb[ RgGgZ R[K^N RUKWM RXS^X RYRRY RVHSO R[HYN RhDgI RcC`IaL',
	    'PobIlI RSN]N RbNkN RbTkT RSU]U RbZlZ RSQ]Q]U RSQSZ^Y^Z RbGb[ RhGhZ RWFYH RRFUH R[F^I RTCRF RYCWF R]C[F RcD_M RhDgJ RXQXU RSJSN RSJ]J]N RbEbL',
	    'PoaIlI RaNkN RRR^R RaTkT RRU_U RaZlZ RbGb[ RgGgZ R\\O^Q RTLWO R[X][ RXW\\Y R\\E^I RXJUM R[KUQ RYFYH RUERJ RcE^N RgDfJ RXWUZR[ RYSWY R^DTE',
	    'PoWLiL RWOiO RWQlQ RcVkV R[ZeZ Rf[k[ RUSU[ RXJXQ R[W[[ R`I`Q RcScV R[WeWeZ RUDUGjEjG RUSkSk[ RXJSL R]UVV R_C_GZHSI',
	    'PocIlI RUI\\I RdNkN RUN\\N RdSkS RUT\\T RdYlY RUY]Y RVGV[ RYIYY R\\K_K_W RdGd[ RhHhY R^D`G RhDiG RYDZG RVERM ReDaM RbV^W ReCdK',
	    'PobIkI RbNkN RbTkT RbZlZ RYD^D^L RcGc[ RgIgY RSFUH RZG\\G RgDgG RXN_Q R^IYK RRDWDXMVORQ RYRTT R[TUW R\\WT[ RWIRK RcCbL',
	    'PoRF_F RbIlI RbNlN RSO]O RbTlT RUX\\X RbZlZ R[[^[ RRRR[ RSHSO RYCXF R^H^O RRR^R^[ RbGb[ RhGhZ RVIZK RZHUM R[T\\X RcD_M RhDgJ RXOWW RbEbL',
	    'PoRF^F RaIlI RaNlN RSN]N RSQ^Q RaTlT RRU^U RaZlZ RSJSN RVCVH R[C[H RSJ]J]N RbGb[ RgGgY RZX]Z RXJXV[Y RXVT[ RcC_M RhDgI',
	    'PoVG[G ReGjG ReJiJ RVJ[J RVQkQ RVTjT RVWjW RVZkZ RWPW[ RVDVM RaOaZ RdDjDjM RTMSNlMlP RXORT R]IbI\\K R]EbE]F R[CUD',
	    'PoWGjG RWIiI RWKjK RWNkN RTR_R RaRlR RUT^T RbTkT RUW^W RbWkW RUZ_Z RbZlZ RUQU[ RXEXO RZPZZ RaDaN RbQb[ RgQgZ RcP_S RYDSJ RVPRS',
	    'PoeIlI R\\NbN RTNZN ReNlN R\\SbS RTSZS ReSlS R\\YcY RTYZY ReYlY RUGU[ RTIYHXY R]F][ R\\IaH`Y ReGe[ RiHiY RWDXG R_D`G RhDiG RUCSM R]D\\L RfCeL',
	    'PoWFjF RWJjJ RaOjO RUS_S RaSlS RRVlV RUMUT R_T_[ RbMbT RXESG RcQfQ R_VeYl[ R[IWI RdI`I RZLZRVQ R]QZQ RdQaQ RiQfQ R_VXYR[ RXEXJ[I RgIdI RcIfIiI RaD`I\\I`IdI RgLgRjQ RUQVP\\P]Q',
	    'PoRElE Re[j[ RTKT[ R_E_[ RTKkKk[ RbNhP RWN\\P RbTgV RWT\\V',
	    'PoTDjD RVJ\\J RbJhJ RUM\\M RbMiM RWPhP RRSlS R`[f[ RSGSK R_D_N RSGkGkK RYSXWgVf[',
	    'PoTEkE RUK\\K RbKiK RTN\\N RbNjN RUViV RTZiZ RRHRM R_E_O RURiRi[ RRHlHlM',
	    'PoTEjE RVK\\K RbKhK RUN\\N RbNiN RRQlQ RSHSM R_E_O R_Q_[ RSHkHkM R_SiX',
	    'PoUDiD RVJ\\J RbJhJ RVM\\M RbMiM RRRlR RSGSK R_D_M R_O_[ RSGkGkK R_QdVlZ R^RYWRZ',
	    'PoUDjD RVJ\\J RbJhJ RUL\\L RbLiL RSGSK R_D_M RSGkGkK RXVS[ R[OWX RZR^VdYl[ RUOdObSiSfWbYZ[',
	    'PoTDjD RUJ\\J RbJhJ RUM\\M RbMhM RaSjS R^SgS RZSdS RTT^T R^WjW RZWeW RXWbW RSW^W R^[k[ RSGSL RUQ_P_[ R_D_N RSGkGkL RkXl[ ReO^P',
	    'PoUDiD RVK\\K RbKhK RUN\\N RbNiN RRQlQ RSHSL R_D_M R_O_Q RSHkHkL RXQ\\UaXj[ RfQbV\\YT[',
	    'PoUDiD RVK\\K RbKhK RUN\\N RbNhN R`[e[ RSHSL R_D_O RSHkHkL RdQlT RYQRT R]UZYT[ RWTfTe[',
	    'PoTDjD RVK\\K RbKhK RUN\\N RbNiN RRQlQ Ra[g[ R_D_M R_O_Q R\\QZWXYR[ RSHRL RSHkHlL R[UhUg[',
	    'PoTDjD RVK\\K RbKhK RUN\\N RbNiN RUQhQ RRUlU RTZiZ RSHSM R_D_O RSHkHkM R[UYWWZ RcWiY',
	    'PoUDkD RcIiI RWI\\I RWL]L RcLiL RUOlO Rd[i[ RTGTJ R`D`L RTGlGlJ R`V]YX[ RaQ_X RXSkSiZ RUOUTTY',
	    'PoUDiD RVK\\K RbKhK RVN\\N RbNhN RSRkR RR[l[ RSHSL R_D_N RSHkHkL R_O`R RXSZZ RfSd[',
	    'PoTDjD RVJ\\J RbJhJ RVL\\L RbLhL RRUlU RSGSL R_D_K RSGkGkL RVPhPhU R_M_TbWfYl[ RVPVU R^T\\XXYR[',
	    'PoUEiE RVJ\\J RbJhJ RUM\\M RbMiM RcZhZ RSHSK R]U][ R_E_L RUUhUhY RSHkHkK R_NjS R_NTS R[RcR',
	    'PoTEkE RUK\\K RbKiK RTN\\N RbNjN RUViV RUZiZ RRHRM RURU[ R_E_O R_R_Z RURiRi[ RRHlHlM',
	    'PoTDjD RVJ\\J RbJhJ RVM\\M RbMhM RRQlQ RXUfU RXYfY RSGSL RXOX[ R_D_N RfOf[ RSGkGkL',
	    'PoUDjD RVJ\\J RbJhJ RUM\\M RbMiM RWWbW ReWhW RX[k[ RSGSK RXSX[ R_D_N RWSbScW RSGkGkK RlXk[ RYNST RWQiPhW',
	    'PoUDjD RVJ\\J RbJhJ RVL\\L RbLiL R\\[`[ RSGSK R_D_M R`S`[ RSGkGkK RRTlThX RUPiO_T\\VTZ RZQcT',
	    'PoTEiE RUK[K RaKhK RTN[N RaNiN RUTiT RUXiX R_[k[ RRHRL RUQUY R^E^O R_Q_[ RUQiQiX RRHkHkL RlXk[',
	    'PoUDjD RVJ\\J RbJiJ RVM\\M RbMiM RRQlQ RXZiZ RSGSL RYSY[ R_D_N RWUiUi[ RSGlGlL R[QVVRX',
	    'PoTEjE RVJ\\J RbJiJ RUL\\L RbLjL RRUlU RZ[_[ RRGRJ R_E_M RTP`O_[ RRGlGlJ RWQZS RgQeT RfO_O RhNaO',
	    'PoUDiD RVI\\I RbIhI RVK\\K RbKhK RXOhO RdTlT Rb[g[ RSGSJ R_D_M RSGkGkJ RUWhWg[ RZNTQ R^V[YT[ RWP^RgS RdQTT',
	    'PoTEiE RVJ\\J RbJhJ RUM\\M RbMiM Rb[k[ RSHSK R_E_M RbNb[ RSHkHkK RhOeQ RlXl[ RbSiW RTPYR R[N[VXYS[ R[TRV',
	    'PoTEjE RUJ\\J RbJhJ RUM\\M RbMiM RRQlQ Rg[j[ RSHSL RUTU[ R\\T\\[ R_E_O RcTc[ RUTjTj[ RSHkHkL R^Q]T',
	    'PoUDiD RVI\\I RbIhI RVL\\L RbLhL RSOkO RSGSJ R_D_J ReUe[ RSGkGkJ R^L`N RYXW[ RYP^ReT RYUYZ ReP^SkU RaSSU',
	    'PoUDjD RVJ\\J RbJhJ RUM\\M RbMiM R[RlR RSGSK R_D_N RcOc[ RSGkGkK RcRjY RYVS[ RcQ[Y RTPYQ RRSWT',
	    'PoUEjE RVJ\\J RbJiJ RVM\\M RbMjM Rc[l[ RSHSK R[Q[T R_E_N RdSdZ R[QkQkT RSHlHlK RRRVT RlXl[ RWXTZ RTOXP RXTVX RbU^YY[ RcOaW',
	    'PoUDjD RVJ\\J RbJhJ RUL\\L RbLiL RWTgT RWWgW Rc[g[ RSGSK RWQW[ R_D_L R_M_Q RWQgQh[ RSGkGkK RdNlR RYORR',
	    'PoUEiE RVJ\\J RbJhJ RbLiL RUL\\L RWNgN RRPlP RWSgS RWUgU RVZhZ RSHSK RVWV[ R_E_L RVWhWh[ RSHkHkK',
	    'PoTEiE RVJ\\J RbJhJ RUM\\M RbMiM R\\SkS RTTZT R]WjW R[[l[ RSHSK R_E_N R]PdPdW RSHkHkK RSPYOTT RYUVYR[ RWY`Z RiOaP',
	    'PoUDiD RVJ\\J RbJhJ RVM\\M RbMiM RUOkO RXRiR RUUlU RSGSK RZTZ[ R_D_N RjVcX R_TbXfZl[ RUOUTTY RbZ]ZW[ RSGkGkK',
	    'PoUEjE RVJ\\J RbJhJ RUM\\M RbMiM R[QlQ RSHSK R\\T\\Z R_E_N RcNc[ RSHkHkK RXVT[ RTOYP RRSWT R\\TjTjYgZ',
	    'PoTDjD RVJ\\J RbJhJ RUL\\L RbLiL RWOlO RRUlU RUYlY Rb[f[ RSGSK R_D_L RSGkGkK RXMSR R\\VbW RWQUZ RWRhRf[',
	    'PoTEjE RVK\\K RbKhK RUN\\N RbNiN RVQhQ RSTkT RR[l[ RSHSL R\\T\\[ R_E_O RbTb[ RSHkHkL RVVXY RhVfY',
	    'PoTDjD RVH\\H RbHhH RVJ\\J RbJhJ RSMkM RRQlQ RUThT RSVkV RS[Z[ RSFSJ R_D_J R_L_S RUOhOhS RSFkFkJ RZXk[ R\\TXXbYU[',
	    'PoTDkD RVJ\\J RcJiJ RUL\\L RcLjL RYQfQ RXThT RZYeZj[ RSGSK RZVZZ R_O_T R_D_L RZVeVeY RUNjNj[ RSGlGlK RUNUTTY',
	    'PoTEjE RVJ\\J RbJiJ RUM\\M RbMjM RVRkR RWTiT RWWiW RWZlZ RSGSK RWPW[ R_E_N RaOaZ RSGlGlK RXORU',
	    'PoTDjD RVJ\\J RbJhJ RUL\\L RbLiL RTNjN RRRlR RRUlU RSGSK R_D_K RSGkGkK RYOZR RdOcR R]RXXkZ ReW`YT[',
	    'PoUDjD RVJ\\J RbJhJ RUM\\M RbMiM RbQkQ RST\\T RbTkT RbWlW RSHSK R_D_M RbOb[ R[XT[ RSGkHkK RSQ\\P[WSW',
	    'PoTDjD RVJ\\J RbJiJ RUL\\L RbLiL RZSaS R[[_[ R_D_M RYO`OaR RWVS[ RTNWP RRRVS RSGSK RZRYVaV_[ RfObZ RSGkGlK RiTkZbZ',
	    'PoTDjD RUJ\\J RbJhJ RUM\\M RbMiM RbQlQ R\\ZkZ R\\T\\[ R_D_M RbNbT R\\TkTk[ RRGlGlK RWYSZ RYVVY RTOZP RRHRL RRSXT',
	    'PoSD]D RZM\\M RTQ\\Q RRT]T RXDXO RRHRI]H]J R[V\\Z RcKfN RfFlL ReCfG RcG^L R`QjQe[ RVTUX RdCbI RTMVMSM R[YSZ',
	    'PoUDjD RVJ\\J RbJhJ RUM\\M RbMiM RUS]S RaSiS RUViV Rb[l[ RSGSK RUPUV R_D_M RcVc[ RaPiPiV RSGkGkK RlXk[ R[VZY R]OUP RYZR[',
	    'PoUEiE RVJ\\J RbJhJ RUM\\M RbMiM RZQkQ R[UjU RY[l[ RSHSK R_E_N RbOb[ RSHkHkK RXVS[ RTOXQ RRSVT',
	    'PoSD]D R_GkG RZN\\N RTQ\\Q R`SjS RRT]T Re[l[ RXDXO RRHRI]H]J R`K`S ReCe[ RjLjV RlXl[ RVVSY RTNVMSM R[V\\ZSZ',
	    'PoUEiE RVJ\\J RbJhJ RVM\\M RbMiM RRR]R R_RlR RSHSK RXOX[ R_E_N ReOe[ RSHkHkK RXS[V RgTlY RcT^Y RVURY RfRhW RdRbV RXQUW',
	    'PoTDjD RUJ[J RbJhJ RUL[L RbLhL R[UjU RRGRK R^D^M RRGkGkK RaQdR RTOXP R^We[ RbNYR RXXRZ RhVbY RcOlR RRSVT',
	    'PoUEkE RVJ\\J RcJiJ RUM\\M RcMjM R^RiR R_XhX Rg[k[ RSHSK R[O[[ R_U_Y R`E`M R_UgUgX R[OlOl[ RSHlHlK RXVT[ RRSWT RTOXP',
	    'PoUDiD RVJ\\J RbJhJ RUL\\L RbLiL RRPlP RRVlV RSGSK RVSVV RZNZQ R_D_M RdNdQ RVShShV RSGkGkK RaXj[ R_Q^W R\\YR[',
	    'PoUDiD RVJ\\J RbJhJ RVL\\L RbLhL RbQlQ RV[Y[ RSGSK RYSY[ R_D_M RSGkGkK RRS^S\\V RYSRY RVP\\S RbQcUgYl[ RcN`T RTO^OYQ RiQgUdX^[',
	    'PoTDkD RUJ\\J RbJiJ RTL\\L RbLjL RSSYS R\\SbS ReSlS RT[W[ R][a[ Rf[j[ RROYOYR R_D_N R[ObObR ReOkOkR ReReVlVj[ RRGRK RSRRVYVW[ R\\R[VbVa[ RRGlGlK',
	    'PoUDjD RVJ\\J RcJiJ RVM\\M RcMjM RSR^R R`SkS R`WkW R`ZkZ RSGSK RYOY[ R`D`M R`P`[ R`PkPk[ RSGlGlK RYPWTRY RYT^W',
	    'PoTEjE RUJ\\J RbJiJ RUM\\M RbMjM RSZYZ R\\ZbZ ReZkZ RSPS[ RSPYPY[ R\\P\\[ R_E_N R\\PbPb[ RePe[ RePkPk[ RRHRL RRHlHlL',
	    'PoSDiD RUJ[J RbJhJ RUL[L RbLhL R`RjR RSR]R R_UjU RSU\\U RSY\\Y RRGRK RSOS[ RSO]O]R R^D^M R`OjOiR RRGkGkK RaWfYl[ RiWdY][',
	    'PoTEjE RVJ\\J RbJhJ RUM\\M RbMiM R[QkQ RZVlV RSHSK R_E_M R`N`U RgNgU RSHkHkK ReXjZ RWVS[ RTOXP RRRWT R^YZ[',
	    'PoTDiD RVI\\I RbIhI RUL\\L RbLiL RSP[P R^VjV RSGSJ R_D_M RSGkGkJ RWMXP RcQeT RiWeZ RdNgQlS RdM`Q\\S RYRXY R[XRY RTRUW',
	    'PoTDiD RVJ\\J RbJhJ RVL\\L RbLhL RTRZR R\\TbT RTVZV RW[Z[ Ra[d[ RSGSK RTOZOZ[ R_D_M R_QdQd[ RSGkGkK RfTkZ RaU\\Z RjQgT RdQgV RaNhO RTOTUSZ',
	    'PoUDjD RVJ\\J RbJhJ RUL\\L RbLiL RTRZR RTVZV RW[Z[ RSGSK RTOZOZ[ R_D_L RbRbZ RSGkGkK RhWl[ RfRiY RlSgU RgY`Z RjQbR RTOTUSZ R^O^U]Z RiN^P',
	    'PoUDjD RVI\\I RbIiI RUK\\K RbKiK RVOiO RUTjT RUVjV RUXjX RSGSJ RUQUX R_D_K RVMVNiMhO RUQjQjX RSGkGkJ RcZl[ R[YR[',
	    'PoTDkD RVJ\\J RbJiJ RbLjL RfSjS RUWiW RUZiZ RSGRK RUUU[ R_D_L R_U_Z RUUiUi[ RSGlGlK R_OkOjS ReObR^S RTOTS RZP\\RRS RUL\\MTO',
	    'PoTDjD RUI[I RbIiI RTK\\K RbKiK RVPgP RSRjR RRGRJ R_D_K RRMRNkMkP RRGkGkJ RdWl[ R`[\\[ R]S`V`Z R^UTX RhT_WT[ RaRTU',
	    'PoUDiD RVJ\\J RbJhJ RUM\\M RbMiM R[RbR RSGSK RWOW[ R_O_[ R_D_M RSGkGkK R^RbU RRRXRZV RVQRY R_P[Y RjOdQ RkSdV RlWc[',
	    'PoTDjD RVJ\\J RbJhJ RUL\\L RbLiL RaPlP RU[X[ Re[i[ RSGSK RXSX[ R_D_M RSGkGkK RcM_Q RRS]S\\W RXSRY RUQ[R R_WjWi[ RSO]OXQ RiOgR`T RdWbY^[ RbQfSlT',
	    'PoUDiD RVI\\I RbIhI RUK\\K RbKiK RaPlP RTQ_Q RVS^S RUV`V Rd[h[ RSGSK R_D_L RTN_N_Q RhMh[ RSGkGkK RcSdV R^X_Z RWXUZ RZVZ[W[ RTNTTSZ',
	    'PoTDkD RVI\\I RcIiI RUK\\K RcKjK RRNaN RTS_S RSU`U RUXaX RRX\\X RV[Y[ RSGSJ RTPTQ_P_S R_D_L RdNd[ RSGlGlJ RiSlV RdNlNhR RZWY[ RkXgX',
	    'PoTDjD RVJ\\J RbJhJ RUL\\L R]OdO RYWlW R[[k[ RSGSK R_D_M R\\TcTc[ RSGkGkK RhPkR RXUS[ R^O[R RRRVS RTNXO RhSaS RbLiMaO',
	    'PoTDkD RUI\\I RbIiI RTK\\K RbKjK RUWiW RUZiZ RRGRJ RUTU[ R_D_K RUTiTi[ R`MkMkS RRGlGlK RbOgO RTOYO RRM]M]QRR RkP`R',
	    'PoUDiD RVH\\H RbHhH RVK\\K RbKhK RVOhO RVRhR RSUkU RRWlW RSFSJ RVMVR RZRZW R_D_K R_M_R RdRdW RVMhMhR RSFkFkJ RcXk[ R[YR[',
	    'PoTDjD RVI\\I RbIiI RUK\\K RbKjK R]PjP R]RjR R^Zg[k[ RSGSJ RRSWSW[ R]M]R R^V^Z R_D_K R]MjMjR RSGlGlJ R`R\\V RTNYP RdVhX RdV`X RZYVZ R^UlUk[',
	    'PoTDjD RVJ\\J RbJhJ RUL\\L RbLiL RYOlO R^QgQ R^SgS RYUlU R\\[k[ RSGSJ R\\U\\[ R^M^U R_D_L RgMgU RSGkGkJ RfWjY RVXS[ RaW^Y RTNWO RRRVS',
	    'PoUDiD RVI\\I RbIhI RVK\\K RbKhK R[QcQ RXWfW RS[k[ RSFSJ RXSXW R_D_K RXSfSfW RSFjFjJ RZX[[ RgMcN RcXb[ RTM\\MWR RaLeQlT RUOXQRT RjOeQ',
	    'PoTDiD RVJ\\J RbJhJ RUL\\L RbLiL RSP^P RbQlQ RRR_R RTV]V RTY]Y RZ[][ RSGSK RTTT[ RVNVR R[N[R RTT]T][ R_D_M RSGjGkK RbQdUgYl[ RcN`T RiQgVdY_[',
	    'PoTDkD RVI\\I RcIiI RUK\\K RcKjK RcVkV R[ZeZ Rg[k[ RSGSJ RTTT[ R[W[[ R_D_K RbTcV R[XeXdZ RTTkTk[ RSGlGlJ RSPkPfR R`O`S RWMgM_PRS R\\WVW',
	    'PoTDiD RVJ\\J RbJgJ RVL[L RbLhL RTS\\S RXV\\V R`ZiZ RSGSK RTNTS RTUT[ RTO\\O\\S R_D_M R`V`[ R`ViVi[ RSGjGjK RcM^R RaPcRkU RaOiOfR_U R\\ZVZ RXSYZT[',
	    'PoTDiD RUI[I RaIhI RTK[K RaKiK R^ShS R]XiX RZ[k[ RRGRK RRMR[ R^D^L RcScZ RRGkGkK RVSYU R^T[W RRNYNVR R`MZP R`OkR R_NhNdP[R RXXUX RePYR',
	    'PoSD]D R`JlJ RZM]M RTMVM RaOjO RTQ\\Q RRT^T Rb[e[ RXDXO RRHRI^H]J ReCe[ RaFjFjO R`QbS ReSkY ReS`X RjQgT RWUSY RVJTJ R\\JZJ R[V\\YSY',
	    'PoTDiD RVI[I RbIhI RVK\\K RbKhK RUO^O RRQlQ RUT^T RUV^V RSGSJ RUQUY R^Q^[ RUMUN^M^O R_D_K RSGkGkJ RlXk[ RfWi[ RgMiO RcMeW RjSeX`YYYRZ R^XVY',
	    'PoTDjD RVJ\\J RbJiJ RUL\\L RbLjL RZOlO RZRlR RZUlU Rd[h[ RSGSK R_D_K RSGlGlK R^OaQ RVWS[ RhOeQ RTNWO RRQVS R`XiWh[ R`V^YZ[',
	    'PoTDjD RUI[I RbIhI RUL[L RbLhL R]PjP R]RjR RSSYS R[TlT RSXYX Rf[j[ RSNSZ RSNYNYX R]N]R R^D^L R]NjNjR R_T[X RaX\\Z RbV`Y RfVb[ R\\WkWj[ RRGRK RRGkGkK',
	    'PoUDkD RVJ\\J RcJiJ RUL\\L RcLjL RRO`O RVQ\\Q RcRlR RbWlW RSV^V RRY`Y Rg[k[ RSGSK RTTTV RVNVQ RYQY[ R\\N\\Q RST^T^V R_D_M RcOlOl[ RSGlGlK RcOcTbY',
	    'PoUDiD RVJ\\J RbJhJ RUL\\L RbLiL R_PkP RTS]S R^TlT R_WkW RUZ]Z RSGSK RTOUVU[ RUU]U]Z R_D_M ReTe[ RSGkGkK RbQbS RhQhS RTO]O]S RTQSZ',
	    'PoUDiD RVI\\I RbIhI RVK\\K RbKiK RePlP RZQbQ RYSbS RTGTJ RWRW[ RZNZQ R^M^P R_D_K R[UaUaZ RaNaQ RTGkGkJ RgVl[ RWMSQ RXPRV RjQgX ReQiWcZ_Z R[UZZ ReMdT',
	    'PoUDiD RUK\\K RbIhI RbKiK RUPiP RURiR RSTkT RVXhX RR[l[ RSGSJ RUNUR RWVWX R\\L\\R R_D_K RbLbR RVVhVhX RUNiNiR RSGkGkJ RZX[[ ReXb[ RYI\\I RWIZI RYIVI R[IXI',
	    'PoSD\\D R_GkG R`JjJ RSMUM RYM[M RSQ[Q R]TgT RRT\\T RWDWN RRHRI\\H\\J RbOb[ RlXl[ RbTfY RbT]Y RfOeR RVVRY R_O_S R`C^I R]MiMiVj[ RZV[YRY',
	    'PoUDiD RVI\\I RbIhI RUK\\K RbKiK RRNlN RbShS RVYhY RSGSK R_D_J R_Q_T RcPcS RWUhUh[ RSGkGkK RgPkT RVTVYS[ RaNaQ RVQST R\\N]P RSP[PZSXS RjOcP',
	    'PoUDjD RVJ\\J RbJhJ RUL\\L RbLiL R_RkR R_UkU R_WkW R][l[ RW[[[ RSGSJ R_N_U R_D_M ReNe[ R_NkNkU RSGkGkJ RTQVS RXPZS\\WSZ R]PRS RYTRV R[NRO',
	    'PoUDjD RVI\\I RbIhI RVL\\L RbLhL RSN]N RaXjX RaZjZ RSGSJ RYQY[ R_D_L RaTa[ RaTjTj[ RSGkGkJ RcM_Q RRS]R\\U R\\OXQRX RaOfRlS RaOjOcR^S RdV`V RU[X[U[ RiVgVjV',
	    'PoTDjD RUJ\\J RbJiJ RRMlM RWQgQ RTSjS RSFSI R_D_J RSOSPkOkQ RSFkFkI R]T`X RcWl[ R`[\\[ RYH\\H RXHVH RWHZHXH RdHbH RcHfHdH RaSTV R_VSX RgUZYS[ RfHiH',
	    'PoTDjD RUI\\I RbIiI RUK\\K RbKjK RUPiP RUSiS RRX]X RaXlX RRZ]Z RaZlZ RRGRK RRUR[ RUNUR RXUXZ RRU]U][ R_D_L R_N_S RaUa[ RfUfZ RUNiNiR RRGlGlK RaUlUl[',
	    'PoUEjE RVI\\I RbIiI RVK\\K RbKiK RTNlN RaRkR RaXhX RV[l[ RTGSK R[O[U R_E_L RaVa[ RfOfU R[QVU ReQhSlU RVR\\R^T ReQcT_U RZWZ[ RTGkGkK RTNTTSZ R^OXO RiOcO',
	    'PoUDjD RVJ\\J RbJiJ RVL\\L RbLiL RUQ^Q RaQkQ RUT^T RaTkT RUW^W RaWkW RUZ_Z RaZlZ RTGTK RUPU[ RZOZZ R_D_M RbPb[ RgOgZ RTGkGkK RVNST RbN`T',
	    'PoUDkD RVI\\I RcIiI RUK\\K RcKjK RTN[N RRP]P R`PkP RTR[R R`RkR RTU[U R`YiY RTZ[Z RSGSK RTWT[ RTW[W[Z R_D_L R`N`R R`U`Z R`NkNkR RSGlGlK RbR^W ReVbX ReVhX Rh[k[h[ R`UlUk[',
	    'PoSD]D R`HlH RTMVM RTQ\\Q RaQkQ RRT]T RaTkT RXDXN RRHRI^H]J RhChM R_OkOkW R`EkElH R`H_KlKkM RaNaWiXlZ R[V\\Z RdCdK`O RVVSY RdY_[ R\\MZM RZYSY',
	    'PoSD\\D RaJlJ RcNjN RTQ\\Q RRT]T RTY\\Y RRYYY Rc[l[ RXDXO RSHRI]H\\J RcFjFjN RZW\\Y RbPdR R^K`M RhTkW R_EaG R]P`P`X\\[ RdTaW RkPhR RVTTY RfCfXcX RTMVMSM RZM\\MYM',
	    'PoUDiD RVI\\I RbIhI RUK\\K RbKiK R`MkM RSN^N RRQ^Q R`RjR R`TiT RTV]V R`ViV RTX]X R`XjX Ra[l[ RZ[][ RSGSJ RTST[ RTS]S][ R_D_K RaRa[ R`PjPjR RSGkGkJ R[O[Q RUOVQ R`L`P',
	    'PoTEjE RVJ\\J RbJhJ RUL\\L RbLiL RTRYR R\\RbR RdRjR RTTjT RR[l[ RSGSK RTOTR RTOYOYR R_E_M R_T_[ R\\ObObR RdOjOjR RSGkGkK RXX\\Y RYVUZ ReVaZ RdWiY R\\O\\R RdOdR',
	    'PoSD\\D RTMVM RcPhP RTQ\\Q RRT]T RTY[Y RRYXY RXDXO RSHRI]H]J R_I_L RcMcP R_IkIkL RZV]Y RiMlP R`M_P RjFhI RcR_V R`FaI RdFdJ RcKgL RbTeXl[ RVTTY RbTiTeX_[ RZM\\MYM RjC`E',
	    'PoUDiD RVH\\H RbHhH RUJ\\J RbJiJ RWM]M RaMgM RWP]P RaPgP RURiR RSTkT RVXhX RR[l[ RSFSI RULUR RWVWX RZLZR R_D_J R_L_R RdLdR RVVhVgX RiLiR RSFkFkI RZX[[ ReXb[',
	    'PoTDiD RVI\\I RbIhI RbKhK RVKZLkM RZQaQ RcQlQ RcXiX RY[l[ RSGSK R]W][ R^O^U R_D_L RcVc[ RgOgU RgPkT R^QZU RgQcU RWVRZ RSMWO RRRVS RSGkGkK RYMXTWZ R`O[O RiOdO',
	    'PoUDjD RUJ\\J RbHiH RbJiJ RUN]N RVWiW RSGSJ R_D_J RfNeR RSRZSiSiY RSGkGkJ RSLSMlLlN RaOiPjQ RbPaR ReUiU RZH\\H RcUfU R`Yl[ RXHVH RXHZHXH RVPYOYQUP R_U\\U R^Y[Y RYP\\P R`UcU RVUZU]U`U RbU_U ReUbU RVSVYZYR[ R`Y]Y',
	    'PoUDjD RVI\\I RbIhI RVK\\K RbKiK RUP^P RUS]S R`ReSlT RUV]V Rg[k[ RTGTK RZNZY R_D_L RcXcZ RTGkGkK RUNUZ_Y^[ ReXeZ RVMSR RfPjP RhVkVk[ R`NjNjQfP RdP`O`VcVfVcV RiVfV',
	    'PoUDjD RVI\\I RbIhI RVK\\K RbKiK RTQ\\Q RST]T R`ReSlT Rg[k[ RSGSJ RXQXY R_D_L RcXbZ RSGkGkJ RYM]P R_X^[ RXMTP RUVUX ReXeZ RfPjP RhVkVk[ R`N`VcVfV RcP`PbOjNjQfP RhVeV R[U\\YRZ',
	    'PoUDiD RVI\\I RbIhI RVK\\K RbKiK RUR]R RUT]T R`ReSlT RUW]W RUZ]Z Rg[k[ RTGTK RVQV[ RZPZY RSNSP^N^Q R_D_L RcXcZ RTGkGkK R_X^[ RVQRU ReXfZ RXLUS RfPjP RhVkVk[ R`N`VdVfV RdP`PbOjNjQfP RiVfV',
	    'PoUDjD RVG\\G RbGhG RSPYOdOkP RYS\\S RhSjS RSY]Y R`YlY RS[][ R`[l[ RSWS[ RXPXU RXWXZ RSW]W][ RRRRS]R]T R_D_I R`W`[ RfPfU RSFSGkFkH R`WlWl[ R_RlRlT RfWf[ ReLhL R\\L_K_M\\L RbL_L RXLVKVMYL RVKhKhMeL RTUVTTS RaUcTaS',
	    'PoTEjE RUIiI RRLlL RVUhU Rc[h[ RVOV[ R_C_L R_O_U RVOhOh[',
	    'PoTFjF RUIiI RRLlL RWShS RWVhV Rc[h[ RWOW[ R_C_L RWOhOh[',
	    'PoSF^F RSI^I RRL_L RTS]S RTV]V RgZkZ RZ[][ RTOT[ RYCYL RTO]O][ RaEaS RfHgSgZ RaEkEkS RlWkZ RdV_[ RfMeV',
	    'PoRF\\F RaGkG RSI\\I RaJiJ RRL]L RTS\\S RTV\\V RX[[[ RTOT[ RXCXL RTO\\O\\[ RlWjZkZ RbC^K R^NhNhTiZ',
	    'PoSE\\E R_FlF R_IkI R]LlL RXN\\N R_TkT Rf[k[ RTKTX RXEXW R_O_[ ReCeL ReOeT R_OkOk[ R]VRX',
	    'Po]FlF RRI[I R^IkI R\\MlM R_SjS R_WjW Rf[j[ RWCWI R_P_[ RdCdM R_PjPj[ RTKUT RZKXV R\\URV',
	    'PoSF]F RSI]I RRL^L R^NlN RST\\T Rg[l[ RZ[\\[ RSOS[ RXCXL RXOXT RSO\\O\\[ ReCeN RgNg[ RlXl[ R_FaK RjEiK RbNaX',
	    'PoSF^F RSI^I RRM_M RaRkR RTU]U Ra[k[ RTPT[ RXCXL RXPXU RaJaZ RgJgR R`KkKkS RlWl[ RdC`K RcGhGfL RTP]P][[[',
	    'PoSF]F RSI]I RRL^L R^OlO RTS\\S R`TjT RTV\\V R`[d[ RY[\\[ RTOT[ RXCXL RTO\\O\\[ ReKe[ R`KjKjU RbFhFfJ RbF^J RcCaH',
	    'PoRF^F RSI^I RaIkI RRL_L RaNkN RaSkS RST]T Rg[l[ RSOS[ RXDXL RXOXT RaDaS RhSh[ RaDkDkS RlWl[ RaZ^[ RcSbY RSO]O]ZZZ',
	    'PoSF]F RTI\\I R_LlL RRL^L ReRkR RTU\\U Re[l[ RY[\\[ RTOT[ RXCXL RXOXT RTO\\O\\[ R`G`J ReCeG RfLfZ R`GkGlJ RbWeZ RaTcY RaP_Z',
	    'PoSF]F RSI]I RRL^L R^PlP RST\\T R`UjU RZ[\\[ R`[d[ RSOS[ RXCXL RXOXT RSO\\O\\[ ReLe[ RdGeJ R`LjLjV RjFkJ R`E^J RiD`E',
	    'PoSF]F RaGkG RSI\\I RaJjJ RRM]M R^TgT RSU\\U RZ[\\[ RSPS[ RXPXU RXCXL RSP\\P\\[ RcOc[ R^MiMjVkYl[ RcTgY RbV^Z R`O`S RcSaX RgOeR RbC^K',
	    'PoSH[H RbHlH RbNkN RbTlT RbCb[ R[D[PZVU[ RRT\\S RSN[N',
	    'PobEkE RTI\\I RbIkI RbLkL RSM\\M RaTjT R^TgT RYTbT RTU^U RbWlW R^XiX R\\XfX RWXaX RSX^X R^[k[ RSE]D]N RUQ_Q^[ RbCbN RgO_Q',
	    'PoVFiF RSIkI RVOhO RbQkQ RbTjT RTT\\T RbWlW RSQ\\P\\W R_C_I RbOb[ RVKhKhO RXDUG RRX[WYYT[ RVKVO',
	    'PoTFlF RWI`I RbIkI RcRkR RXU^U RcUjU RcXlX R[G[O R^K^O R`C`F RcPc[ RfGfO RWR^Q^WZ[ RbKbO RXKWO RjKiOlNlO RTFTSS[ R^WVX R`WXX',
	    'PoRElE RZPdP RZUdU RTZjZ RTKT[ RZKZZ RdKdZ RTKjKj[ R_E]J',
	    'PoRElE R[UdU RTYjY RTKT[ R[O[U R[OdOdU RTKjKj[ R^E]K',
	    'PoRE_E RbRgR ReVjV RSY^Y Rb[k[ RSJS[ RVJVY RXEWJ R[J[Y RSJ^J_[ RbJbZ RaLgLgR RlWl[ RcCaL RbHkHkQjU R[OVO R[TVT',
	    'PoUDlD RWIlI RWOkO R]WeW RXZjZ RXSX[ R]S]Z R]UeTeZ RXSjSj[ R`O_R RdGhG RbKgMlN RaF^LWN RUDURS[',
	    'PoRE^E RaIkI RaNkN RaSkS RSX^X Rg[l[ RSISZ RWIWX RZIZX RSI^I^X RaDaS RhSh[ RaDkDkS RlWl[ RaY][ RcSbY RWEWNZNVN RWSZSVS',
	    'PoRE]E R`IjI R^KkK R`QjQ R`TjT R`WjW RSY]Y RSJS[ RVJVY RYJYY RSJ]J]Z R`F`I R`N`W ReCeK R`FjFjI R`NjNjW RgXlZ RbY^[ RWEVNYOVO RVTYTVT',
	    'PoTDlD RaJkJ RTSkS RU[j[ RUUU[ RWLWQ R`S^U RUUjUj[ RgMkQ RiFjH ReMaQ R\\O`O R\\U\\Y_Y R\\WcVcY_Y RZOWN_M_Q\\PZO RXF]G_G_J[IXGXIZI[J RTDTKSP RfGfKeN R_H[H',
	    'PoSFlF RYJeJ RURiR RRVlV RUMUR RYCYJ R_J_[ ReCeJ RUMiMiR',
	    'PoRF_F R`FlF RUJ\\J RSR^R RRV_V Rc[g[ RSMSR RUCUJ RYJY[ R\\C\\J RSM^M^R RgFgZ',
	    'PoRF_F RUJ\\J RSR^R RRV_V RSMSR RUCUJ RYJY[ R\\C\\J RSM^M^R ReTk[ RaLfW RdHeL RjKfTaY][ RaIcS R`EkFhR',
	    'Po`ElE RRF_F RUJ\\J R`NlN RSR^R RRV_V RSMSR RUCUJ RYJY[ R\\C\\J RSM^M^R RfEf[',
	    'PoRF_F RUJ\\J RTR^R RRV_V Re[j[ RTMTR RUCUJ RYJY[ R\\C\\J RTM^M^R R`LdN ReEdNhR RaW^[ RdQ`Y R`ElElTj[ ReHcU',
	    'PoRF_F RUJ\\J RaPiP RSR^R RRV_V Rd[j[ RSMSR RUCUJ RYJY[ R\\C\\J RSM^M^R RcD`M RbIlHlSj[',
	    'PoRF^F RUJ[J R_LkL RSR]R RRV^V R^ZlZ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R ReDeZ',
	    'Po`FlF RRF^F RUJ[J RSR]R RRV^V Rh[l[ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R RhFh[ RlWl[ RcFcQbW',
	    'PoRF^F RUJ[J RSR]R RRV^V Rd[i[ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R RaW][ R`IbPgTlV RcR_Y R_EjEjTi[ RdEdKbU',
	    'PoRF]F RUJ[J RSR\\R RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R RcKfN RfElL ReCfG RcG^L R_QjQe[ RdCbH',
	    'PoRF^F RUJ\\J RaMlM RSR^R RRV_V RSMSR RUCUJ RXJX[ R\\C\\J RSM^M^R RhMh[ RaFaR_Y RkDaF',
	    'PoRF^F RUJ[J RTR]R RRV^V Rf[l[ RTMTR RUCUJ RXJX[ R[C[J RTM]M]R RbGb[ RfDf[ RlWl[ RlIeO RbE^N',
	    'PoRF]F RUJ[J R^OlO RSR\\R RRV]V R]ZlZ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R_EjEiViZ RcFcMbRbUaZ',
	    'PoRF^F RUJ[J R`OkO RSR]R RRV^V R`[k[ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R RaEaZ ReEeO R`EkEkP RlWk[',
	    'PoRF]F RUJ[J R`KfK RSR\\R RRV]V Ra[e[ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R_EfEfL RlDl[ R`K_RgQe[',
	    'PoRF]F RUJ[J RSR\\R RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R ReUj[ R`EaOfW R`MkMhUcY_[ R`K^X R^EiEhM',
	    'PoRF^F RUJ[J RSR]R RRV^V Ri[l[ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R R`J`[ R`JlJl[ RgNjU RbDfDfJhR RfFdR',
	    'PoRF^F R^HlH RUJ[J R_NkN RSR]R RRV^V RSMSR RUDUJ RXJX[ R[D[J RSM]M]R ReCe[ RhTlX RbT^X ReMiU ReMaV',
	    'PoRF]F RUJ[J R`LjL RSR\\R R`SjS RRV^V R][l[ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`E`Z R`EjEjZ',
	    'PoRF^F RUJ[J RaMjM RSR]R RaUjU RRV^V R^ZlZ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R RaEaT RaEjEjT',
	    'PoRF^F RUJ[J R`MkM RSR]R R_SlS RRV^V RSMSR RUCUJ RXJX[ R[C[J RSM]M]R ReCe[ RiEkJ RbE_J',
	    'PoRF\\F RTJZJ R\\KlK RSR[R RdTiT RRV[V R_ZlZ RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R_D_Y RdCdT RiDiT',
	    'PoRF\\F RTJYJ RSR[R RRV\\V RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R\\K`O RfRb[ RbM]W R`D]L Rj[g[ RaRcV[V RcIlIlTj[ RgCgMeW',
	    'Po`ElE RRF^F RUJ[J RgNkN RSR]R RRV_V RaZlZ RSMSR RUCUJ RXJX[ R\\C\\J RSM]M]R RaQa[ RaQlQl[ RdJ`N ReEcL RlFkN',
	    'PoRF^F RUJ[J RSR]R RRW_W RSMSR RUDUJ RXJX[ R[D[J RSM]M]R ReCeN RdTj[ RbNfW R`NkNgVcZ`[ R`HkHkL R`G`R^Z',
	    'PoRF]F RUJ[J R_LlL RSR\\R RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM]M]R RfEf[ ReJkV ReK`V RjD_E',
	    'PoRF[F RSRZR RRV[V RSMSR RTCTJXJW[ RYCYJ RSM[M[R RgDcY R]EbEbJ^K]QaQ`Z\\Z RiPkYbY',
	    'PoRF^F RUJ[J R`RgR RSR]R RgUjU RRV^V Ra[k[ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R RaLa[ R`LgLgR RaHkHkT RlWl[ RbC_K',
	    'PoRF]F RUJZJ R^QlQ RSR[R RRV]V RSMSR RUCUJ RXJX[ RZCZJ RSM\\M\\R R`H`Q R`HjHjQ RgWk[ RcV^[ ReCeQhY RdNcV',
	    'Po_ElE RRF^F RUJ[J RSR\\R RRV^V Rh[l[ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R_J_[ R_JlJl[ ReEeNgPiT ReIdR',
	    'PoRF^F RUJ[J RaLjL RSR]R RRV^V R`ZlZ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R R`D`[ R`DlDl[ RfGfOjW RfOcV RfJeT',
	    'PoRF^F RUJ[J R`LhL RSR\\R RRV]V R^ZiZ RSMSR RUCUJ RXJX[ R[C[J RSM]M^R^[ R^QiQi[ ReFlM RbG\\M RdCfH RcCaI',
	    'PoRF\\F RTJZJ RSR\\R RRV\\V Rf[l[ RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R RgCg[ RlXl[ RfOiQkT RbP_S RaV\\[ R^H`M RkHiL RbCbQaW',
	    'PoRF]F R^HlH RUJZJ R_NkN RSR\\R RRV]V R_ZjZ RSMSR RUCUJ RWJW[ R[C[J RSM\\M\\R R_R_[ ReCeN R_RjRj[',
	    'PoRF_F R`GkG RUJ\\J R_MlM RSR]R R`SkS RRV^V R^ZlZ RSMSR RUCUJ RXJX[ R\\C\\J RSM]M]R RfCfM RfOfZ',
	    'PoRIlI RROlO RYQeQ RVViV RRYlY RVSVV RSMZMYQ R_Q_[ ReNeQ RVSiShV R_CaE R\\GXK RSESFlEkG R_KjM RfIaKWM',
	    'PoRF]F RUJ[J R^NlN RSR\\R RRV^V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R^G^J R^GlGlJ R`TjY RhTdY^[ ReCeG RiNgV RcJ`U',
	    'PoRF\\F RTJZJ R_JjJ R_OjO RSR\\R RRV]V RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R R_E_[ R_EjEjO ReUkZ RcOgW RkSfU RdY^Z',
	    'PoSE\\E RgJlJ RRMkM RYPeP RVUhU RRXlX RVRVU RYKYP R_P_[ ReKeP R`DhDhJ RVRhRhU RlHlJ RbGdI R_H\\K RWEWJ R`D_I R\\IRJ',
	    'PoRF_F ReGkG RUJ\\J R_LlL R_PlP RSR^R RRV_V RSMSR RUCUJ RYJY[ R\\C\\J RSM^M^R ReCeL RePe[ ReSkU',
	    'PoRF[F RaJlJ RSRZR RRV[V Rb[l[ RSMSR RTCTJXJW[ RYCYJ RSMZMZR R\\M_M_X\\[ R^F^I RfCfMkW R_Xd[ RfObW RfHeS',
	    'PoRF\\F RTJZJ R^JlJ RSR[R RRV\\V RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R RhQh[ RgKlP RcL^Q RdGaO R_Z][ RdEbM RbQaX RiD_E',
	    'PoRF\\F RTJZJ R_KjK R^OkO RSR\\R R_TkT RRV]V R]ZlZ RSMSR RTCTJ RXJX[ R[C[J RSM\\M\\R R_D_K RdOdZ R_DjDjK',
	    'PoRF]F R_RkR RSR[R RRV\\V Rf[l[ RSMSR RUCUJYJW[ RZCZJ RSM[M[R R`I`R RgRgZ R^KkKkR RlWl[ RbC_K RcV`Z\\[ RbFhFeKcV',
	    'Po^EkE RRF]F RUJ[J R_MkM R_RkR RSR\\R RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R_H_Q R_HkHkQ R`TdXj[ RdV`Y][ ReEeQdV',
	    'PoRF]F RUJ[J R^PlP RSR\\R RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R RdGeK RiFlL R`Uk[ RdM_V RiPgVdX^[ R`E^L RjD`E',
	    'Po_FlF R\\NkN RaPgP R^VjV R\\YlY RWGW[ RZGZW R^S^V RaLaP RdPd[ RgLgP R^SjSjV RaC\\H RXDSM R`HeJlL RhFfHbJ\\L',
	    'PoRF^F RUJ[J R`PkP RSR]R R`TkT RRV^V Rg[k[ RSMSR RUCUJ RXJX[ R[C[J RSM]M]R R`K`[ ReCeK R`KkKk[ RiElH RaE_I',
	    'PoRF^F RaJlJ RUJ\\J RaRlR RSR]R RRV_V RaVlV Ri[l[ RSMSR RUCUJ RXJX[ R\\C\\J RSM]M]R RaMa[ RaDaJ RaDlDlJ RaMlMl[',
	    'PoRF]F RUJ[J RSR\\R R_RkR RRV]V R^ZlZ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R_G^J ReCeG ReReZ R^GlGlJ RhGgOlNlO RaN_O RcGbM',
	    'PoRF]F R_GkG RUJ[J R^NlN RSR\\R RRV]V R`ZjZ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`R`[ RdCfG R`RjRj[ RaHbM RiHgN',
	    'PoRF]F RUJ[J RbOeO RSR\\R R`UkU RRV]V R`ZkZ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`Q`[ ReCeO R`QkQk[ RgIkM RjFgI ReDhJ R_GcG`L',
	    'PoRF\\F RTJZJ RSR[R RbVhV RRV\\V Rh[k[ RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R^L^[ RbObW ReCeK RbOhOhV R^LkLk[ RkFhI R^E`I',
	    'PoRF]F RUJ[J R`KkK R`PkP RSR\\R R_UlU RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`F`P RgQg[ R`FkFkP RdCeFePaT',
	    'PoRF]F R_FlF RUJZJ R`NjN RSR\\R R^VlV RRV]V RSMSR RUCUJ RXJX[ RZCZJ RSM\\M\\R R`I`N ReCeF ReTe[ R`IjIjN R_QjQeS Rb[e[a[',
	    'PoRF]F RUJ[J R_QiQ RSR\\R RRV^V Rg[k[ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R RdJdZ RcQhV RbT^X R`LaO RbC^L RdPaU RhKgO R`HlHlVk[',
	    'PoRF[F RcIkI R\\IaI RSRZR RRVZV RaZlZ R\\[_[ RSMSR RTCTJXJW[ RYCYJ RSMZMZR R_C_[ RgDgG RaQ[R RdLeW RjLgZ',
	    'PoRF]GlG RUJ[J RdLiL R`OkO RSR\\R R^SlS RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`D`O RdCdK ReOe[ RiDiK RfTlZ RdT^Y',
	    'PoRF]F RUJ[J RbPeP RSR\\R R^SlS RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R ReQe[ RfHfO ReSkY RbL^N ReI`N R^IlIjM RdS^Z RbFhI R_EjEdG',
	    'PoSE]E R`FlF RRH^H RSJ^J RdMhM RRM^M RRPlP RYReR RVVhV RRYlY RXCXG RXIXM RYNYR R_R_[ ReNeR RhChL RVTVUhThW RbHdK',
	    'PoRF]F RaIiI R`QjQ RSR\\R R^VlV RRV\\V RSMSR RUCUJYJX[ RZCZJ RSM\\M\\R R`L`Q RhRh[ R`LjLjQ RbX_[ ReDhGlH ReCaG^I RbRbY',
	    'PoRF[F R\\KcK RSR[R RRV[V RSMSR RTCTJXJW[ RYCYJ RSM[M[R RaDa[ R`NcQ RfSc[ R`K\\T RhChQkZ RgPdY RkIjO ReJdO RcD\\E R`H^Q RgKfW RhLiW',
	    'PoRF\\F R^HlH RTJZJ RfRkR RSR[R R_VkV RRV\\V R_ZkZ RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R_M_[ RcHcQ RgHgQ R_MkMk[ R_C`F RjEfH',
	    'PoRF]F R`HkH RUJ[J R`LkL RSR\\R RRV]V R`XiX Rh[k[ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`P`Y R`DkDkL ReShV RaO]S ReSbV R`DaL`QlPlVk[',
	    'PoRF\\F R]PeP RSR[R R]UeU RRV\\V Rb[e[ Ri[l[ RSMSR RTCTJXJW[ RZCZJ RSM[M[R R]K][ RaCaK R]KeKe[ RhFhU RlDl[ R]E^I ReEcI',
	    'PoROkO RYQdQ RVVhV RRYlY RVSVV RVLYMYQ R_Q_[ ReMeQ RVShShV RRH_H]J RcDaFfJlL RbFjFfJ`L RXGVJRK RVF]G RTD^DZFYM',
	    'PoRF]F RUJ[J R`JgJ R^MiM R`OgO R`RgR RSR\\R RRV^V R`XgX RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`T`Y R`TgTgX RaC^K Rk[g[ R`HlHlVk[',
	    'PoRF[F RaJlJ RbMjM RbQjQ RSRZR RaUlU RRV[V Rc[l[ RSMSR RTCTJXJW[ RYCYJ RSMZM[R RfCfX RbFjFjM R^VbZ R]R`X R\\E_E]N`N^X',
	    'PoaElE RRF[F RbMkM RbQkQ RSRZR RRV[V RSMSR RTCTJXJW[ RYCYJ RSMZMZR R_G_[ RbIbQ RbIkIkQ RbTfXb[ RdXl[ R_D^M RfEfReX',
	    'PoRF]F R`HjH RUJ[J R`LjL R^OkO RSR\\R ReTkT RRV]V Rd[l[ RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R`D`L ReOeZ R`DjDjL R`UbY R`T][',
	    'Po_FlF RRF]F RTJZJ R^KlK RSR\\R RcTlT RRV]V Re[h[ RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R RaOa[ RbCbH RhRh[ RiCiH RaP]T RdOlOhR RdI_S',
	    'PoRF]F R_IjI RTJ[J R_NjN RSR\\R RRV\\V R\\[l[ RSMSR RTCTJ RXJX[ R[C[J RSM\\M\\R R^R^Z R_E_N RcRcZ RgRgZ R_EjEjN R^RkRkZ',
	    'PoRF^F RUJ[J RSR\\R R_TdT RfTkT RRV^V R_ZkZ RSMSR RUCUJ RXJX[ R[C[J RSM\\M]R R_O_[ ReGeK RfNkNk[ RiFlK RaE^K RdM_O RiD`E',
	    'Po^FkF RRF]F R_IkI RUJZJ R^LlL RSR\\R R^SlS RRV\\V R^WlW Rg[j[ RSMSR RUCUJ RWJW[ RZCZJ RSM\\M\\R R_O_[ RbCbL ReLeV RhChL R_OjOj[',
	    'PoRF]F R^FkF RUJZJ RaPiP RSR\\R RRV]V R`ZjZ RSMSR RUCUJ RWJW[ RZCZJ RSM\\M\\R R`T`[ RbCbI RhChI R`TjTj[ RdIlP RdJ^P',
	    'Po^FlF RRF\\F RTJYJ R^JlJ RaRkR RSR[R RRV\\V RaVkV Ri[k[ RSMSR RTCTJ RWJW[ RYCYJ RSM[M[R RaNa[ RbCbH RfNfZ RhChH RaNkNl[ R^J^T]Z',
	    'PoRF]F RUJ[J R^RkR RSR\\R R^UlU RRV]V RSMSR RUCUJ RXJX[ R[C[J RSM\\M\\R R_LdN RhOlR RjFkJ RcJaL RgKaQ RdFeI ReTfWiZl[ R`E^J RdWaY][ RdQcX RiD`E',
	    'PohHlH R`IiI RRI^I R[M]M RSPkP RYReR RVVhV RRYlY RUEUK RVTVW RYJYM RYNYR RUE]E]M R_R_[ RbDaH ReNeR RbDhDhH RVThThW RgK_M RcLlN',
	    'PoSDkD RVHhH RVLhL RgNjN RRPlP RYReR RVViV RRYlY RTJTN RYOYR R_R_[ ReOeR RVFWGhFhH RVTVUhThV RTJjJjN R_L_N',
	    'PoRF\\F R_HkH RTJZJ R]PlP RSR\\R R^SlS RRV\\V R^WkW RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R R_D`L`S ReDe[ R_LjLiS R_DkDkL',
	    'PoRF[F R]FdF R]MdM RSR[R R\\UdU RRV[V R^[`[ RSMSR RTCTJXJW[ RYCYJ RSM[M[R R]I]M R`C`G R`S`[ R]IdIdM RfEf[ R]PdP`T RfElEiMlSlWiW',
	    'PoRF]F RTJZJ R`QiQ RSR\\R R_UjU RRV]V R]ZlZ RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R R^H^L R`L`Q RdCdH RdQdZ R`LiLiQ R^HlHlL R_DaG RjDhG',
	    'PoRF\\F R\\HlH RTJZJ R_KjK R^QkQ RSR[R R\\TlT RRV\\V RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R^M^Q R_E_K RdCdP R_EjEjK R^MkMjQ RbQ_WkZ RhUeY][',
	    'PoRF\\F R_IjI RTJZJ R]KlK R_QjQ RSR\\R R_TjT RRV]V R_WjW RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R R_E_I R_N_W ReCeK R_EjEjI R_NjNkW RgXl[ RbX\\[',
	    'PoRF[F R]HlH R_LjL RaOiO RSR[R RRV[V RbXhX RSMSR RTCTJXJW[ RYCYJ RSM[M[R R^Q^[ RbTbY RbThThX RhIiO RfHkM RcH]M R^EdEaJaN R^QkQk[g[ RiDaD',
	    'Po^FlF RRF\\F RTJZJ R]NlN RSR\\R R^SlS RRV\\V R^WlW RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R R`J`S RbCbH ReJe[ RgCgH R^JjJiS',
	    'PoRF[F R^QlQ RSR[R RRV[V RSMSR RTCTJXJW[ RYCYJ RSM[M[R RlXk[ RfXi[ R_TbW R]H_J RfGhJ RhOjP RjGgM R_D^H RaG^N RhDfH RjTeX_[ RaLbN\\N RdCdLfX R^Q]Z RkKkNeM',
	    'Po^FlF RRF[F R^MkM RaQlQ RSR[R RRV[V Rh[k[ RSMSR RTCTJXJW[ RYCYJ RSM[M[R RcVcY RgFgM R^JkJkM RhUjW RfVfX R`V`Z RaTlTk[ R^F^S\\[ RcFcKaT',
	    'Po^DlD RRF]F R_JjJ RTJZJ R_MjM R^OlO RSR\\R R_UjU RRV]V R_XjX R][l[ RSMSR RTCTJ RWJW[ R[C[J RSM\\M\\R R_R_X R_G_M ReGeM ReReX R_GjGjM R_RjRjX',
	    'PoRF[F R]IlI R`OlO RaQjQ RSR[R RaTjT RRV[V RaZkZ RSMSR RTCTJXJW[ RYCYJ RSM[M[R RaVa[ RaVkVk[ ReLfO RgJkK RdJaL R`EfEeH RaD]H^S\\Z',
	    'PobFkF RRF[F RaJlJ RaNkN RbRkR RSR[R RaUkU RRV\\V Rb[l[ RSMSR RTCTJXJW[ RYCYJ RSM[M[R RfCfJ RfNfY R\\P_P_XbZ R\\J_L R^D_G R_W\\Z RdKdN RiKiN',
	    'Po]EkE RRF\\F R_KkK RTJZJ R_MjM R_OjO R_RkR RSR[R RRV\\V RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R`H`R RbCbG ReHeR RgCgG RaWgZl[ R^UjUgWcY\\[',
	    'PoRF\\F R_HjH RTJZJ R_KjK RSR[R ReUkU RRV\\V RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R R_D_K RbWc[ R_DjDjK R]UcTcU RjRkU RbOaS RaL^OaR^U RhLfOhReU RfWg[ RjWk[ RiOhS R_W][',
	    'Po`DkD RRF[F RaLkL R`NlN RSR[R RRV\\V Rb[l[ RSMSR RTCTJXJW[ RYCYJ RSM[M[R RaHaK RgDgK RaHkHkK R\\P_P_WbZ R\\J^L R]D_F RgNlS R_W[Z ReN`S RdRcXjWjX RdDeKcRhRhUfU',
	    'Po^EkE RRF\\F R_HkH RTJZJ RfLkL R^NkN RSR\\R R_SjS R_UjU RRV]V R_XjX RSMSR RTCTJ RWJW[ RZCZJ RSM\\M\\R R^J^N R_P_W RdCdG RfJfL R_PjPjW R^JkJkN RcJaL RgYlZ RbY\\[',
	    'Po]FlF RRF\\F RTJZJ R^RlR RSR[R RRV\\V RSMSR RTCTJ RWJW[ RZCZJ RSM[M[R RbCbH RgIgN RhChH R^IkIkN RlXl[ RfXj[ R^I^NiOjP R^UaV RjTeYb[ RbIcNfX R^R]Z',
	    'Po]ElE RRF[F R]IlI RTJYJ R]PkP RSR[R R`SlS RRV[V RSMSR RTCTJ RWJW[ RYCYJ RSM[M[R RaCaG RcWcZ RdGdJ RgIgP RhChG R]LkLkP RhWiX ReWfY R]I^W^[ Rk[h[ R`UlUk[ RbIaP_V R]Q\\Z',
	    'Po\\ElE RRF[F R]KbK RfKlK R]NbN RfNlN R_PiP RSRZR RRV[V Ri[k[ RSMSR RTCTJXJW[ RYCYJ RSMZMZR R]I][ RaCaG R]IbIcN RdOdZ RfIfN RhChG R`RhRhV RfIlIl[ R`R`VfUhY RdU`Y RfRfU',
	    'PoRJlJ RVQhQ RVTiT RRXlX RVLVQ RbQb[ RVFgEgJ RVLhLhQ RVTVX R\\C[J',
	    'PoRJ`J RTP^P RTS_S RRWaW Rf[j[ RTLTP R[P[[ RTL^L^P ReRbZ RTF]F^J RXCWJ RTTTW RbKaR RaElElTk[ RgEgLdV',
	    'Po`IlI RRJ^J RTP]P RTS^S RRW_W RTLTP RZPZ[ RTL]L]P RfVl[ RgDjG RfVa[ RdOhX RiOeX RSF\\F\\J RcCcLfV RjLgV RWCVJ RTSTW RbO_Z RcI`W RcHdS',
	    'Po_HlH RRJ^J R`NkN RTP]P RTS]S RRW^W RTLTP RYPY[ RTL]L]P ReCe[ RbU^X ReLjW ReMaW RSF\\F\\J RWCWJ RTSTW',
	    'PoUFjF RRIlI RTOjO RWTgT RTVjV RVZkZ RVVVY RWQWT R`T`[ RWLfLfO RWQgQgT RWDZF RfDcF RcIkM R]K[O R^EZJRM',
	    'PoRI^I RaLiL RTP\\P RTS]S RRX^X R`ZjZ RTLTP RYPY[ RTL\\L\\P R`Q`[ R`QjQj[ RgGlL RbH^L RfChH RdCbI RSF\\F\\I RWCUI RTSSX',
	    'Po]IlI RRM]M R_OjO R_RkR R]VlV RZZkZ RXCXL RZMZZ R_R_V RfOfX R_FiFiI R_LjLjP RkXlZ R\\EZK RcCbI RSEUK R_L_P RUMURTY',
	    'PoRJ^J R`PkP RTP\\P RRS]S R`UkU RSX]X Rg[k[ RTLTP RTTSW RYPY[ RTL\\L\\P R`K`[ RfCfK R`KkKk[ RkEjH R`EbI RSF\\F\\J RWCVJ',
	    'PoRG]G R^JlJ RSN\\N R`QjQ RSR\\R R`TkT RSJSR RXCXJ RXRX[ RSJ\\J\\R R`L`Q RfQf[ R`LjLjQ R_FiFiJ RcCbJ R`T_X RRV[V R]WkW',
	    'PoaDjD RaHjH RRJ^J RaLjL R^PlP RTP\\P RTS]S RRW^W RTLTP RYPY[ RTL\\L\\P RaDaZ RfVk[ RkRhU RdQhX RSF\\F\\J RWCWJ RTSTW RfY^[',
	    'Po_FlF RRJ]J R`NjN RTP\\P RRS]S R^VlV RSX\\X Ra[e[ RTLTP RTTSW RXPX[ RTL\\L\\P R`I`N ReTe[ R`IjIjN ReCeF R_QkQeS RSF[F\\J RWDVJ',
	    'PoRJ]J RbOeO RTP\\P R^SlS RRS]S RSX\\X RTLTP RTTSW RXPX[ RTL\\L\\P ReQe[ RfHfN ReRlY ReI`N RdS^Y RbFhI R_IkIiM R_EjEdG RSF[F[J RWCVJ',
	    'Po_IkI RRJ]J R_PkP RTS\\S R_SkS R^WlW RRW]W RTLTPZPY[ RTL\\L\\P R_E_H R_M_S ReGe[ R_MkMkS R_DkEkH RSF[F[J RWCVJ RTSTW',
	    'Po^GlG RRJ]J RdKiK R`OkO RTP\\P R^SlS RTS\\S RRW]W RTLTP RYPY[ RTL\\L\\P R`D`N RdCdK ReOe[ RiDiK RfTlZ RcT^Z RSF[F[J RWCUJ RTSTW',
	    'PoSH]H R^IlI RSL]L RRO^O R`OjO R`RkR RXT]T R]VlV RZ[l[ RSDSL RSD]D]L R`K`O RfOfY R`KjKjO RTUXZ R_EiEiI RcCbI R`R`V RURS[ RYOXY`Z',
	    'Po^GlG RRJ]J RaJiJ RTP[P R_SkS RRS\\S R^VlV RSX\\X RTLTP RTTSW RXPX[ RSF[F[I RTL[L\\P R_N_S RaDaJ ReJe[ RiDiJ R_NkNkS RWCVJ',
	    'Po^FlF RRJ]J R_JlJ RbRkR RTS\\S RbVkV RRW]W Rh[k[ RTLTPZPY[ RTL[L[P RbNb[ RbCbH RfNf[ RhChH RbNkNk[ RSF[F[J RWCUJ RTTTX R_J^U][',
	    'PoRI^I RTP\\P RTS]S R`TdT RgTkT RRX^X R`YkY RTLTP RYPY[ RTL\\L\\P R`O`[ RdGeK RfOkOk[ RiGlL ReN`O RSF\\F\\I RWCUI RTSSX R`E_L RjCaE',
	    'Po^FlF R_IkI RRJ]J R^LlL RTP\\P R^SlS RTS\\S R^WlW RRW\\W Rg[j[ RTLTP RYPY[ RSF[F[J RTL\\L\\P R`O`[ RbCbL ReLeV RhChL R`OjOj[ RWCVJ RTSSW',
	    'PoRJ^J R`NjN RTP]P RTS]S RRW^W R]ZlZ RTLTP RTSTW RZPZ[ RTE\\E\\J RTL]L]P R_Q_Z R`E`N RcQcZ RgQgZ R`EjEjN R_QjQjZ ReIhL ReFcL RVCVJ',
	    'Po_IjI RRJ]J R_MjM RTP[P R^QlQ RRS\\S R_WiW RSX\\X RTLTP RTTSW RXPX[ RTL[L\\P R_F_M ReSe[ R_FjFjM RfQlV RcP]V RdCbE RdMaS RSF[F[J RWCVJ',
	    'Po_HjH RRJ^J R_LjL R^PlP RTP\\P RTS\\S R^TkT R_WkW RRW^W RTLTP RYPY[ RTL\\L\\P R_D_L RaMaT ReDe[ RiMiT R_DjDjL RSF[F[J RWCUJ RTTTX',
	    'Po]FlF RRJ\\J R]OlO R^SkS RTS\\S R^WlW RRW\\W RTLTPZPY[ RTL\\L\\P R`K`S RbCbI ReKe[ RgCgI R^KjKiS RSF[F[J RVCVJ RTSSW',
	    'Po]ElE RRJ\\J R^KjK R^OjO RTP[P R^SkS RRS\\S RSX\\X RTLTP RTTSW RXPX[ RTL[L[P R^H^O RdCdR RbUaZhYh[ R^HjHjO RcTfW RhPlS RiUkZ R^V]Z RSF[F[J RWCVJ',
	    'Po_IlI RRJ]J RTP\\P RRS]S R_UhU RSX\\X R^YhY Rh[k[ RTLTP RTTSW RXPX[ RTL\\L\\P R_E_I R_Q_T RdNdX RgEgI R_QhQhT R_ElElI RgVhY RaK]O RcEcHaL RSF[F\\J R`MlMlWk[ RWCVJ',
	    'Po]FlF RRJ]J R^NkN R^RlR RTS\\S RRW]W RTLTPZPY[ RTL[L[P R^I^M RbCbH RbIbM RgIgM RgCgH R^IkIkM RlXk[ R`UbW RiTc[ RgOjP RhZk[ RSF[F[J RVCVJ RTSSW ReOgY R^S^Z',
	    'Po^ElE R_HkH RRI\\I R^NkN RTP[P R_SjS RRS\\S R_UjU RSX\\X R_WjW RTLTP RTSSW RXPX[ RTL[L[P R^J^N R_P_W RbJbN ReCeH RgJgN R_PkPkW R^JkJkN RgYl[ RbY][ RSF[F[J RWCVJ',
	    'PoRGlG RTLjL ReYjY R_C_[ RRRlRkY',
	    'PoRG]G RSL]L Re[j[ RXCX[ RcV^[ RePbY R]WZW RbJ`Q R`FlFlTj[ RRQ]Q]W RfFfLdU',
	    'PoRG\\G R^IlI RSL[L RWDW[ RfDjG RdY_[ RhTbZ RbCbOdUhYl[ RaNjNfW R`R][ RYX\\WYW RRQ\\Q\\W RbI_W',
	    'PoRF]F R_GlG R_LjL RSN\\N RSR\\R RRV]V RSJSQ RXCXJ RXRX[ RSJ\\J\\Q ReCe[ RkXhX R^QlQkX',
	    'Po_GkG RTH\\H R`KjK RTL\\L RRO]O RYT]T RgVjV R\\[l[ RTDTL RTD\\D\\L ReDeX RTUXY R_PkPkV RURS[ RYOYYbZ',
	    'PoRG\\G R_IjI RSL\\L R_NjN R\\[l[ RWDW[ R_R_[ R_E_N RcRc[ RgRg[ R_EjEjN R_RkRk[ R[WYX RRQ\\Q[X',
	    'PoRG\\G RSL\\L R_TdT RgTlT R_ZlZ RWDW[ R`O`[ RgNlNl[ R_HaL ReGeK RkGiL RdN_O R\\WYW RRQ\\Q\\W RkD`F',
	    'PoSI[I RcIkI RTO[O RcOkO RSU[U RcUkU RR[l[ R[D[[ RcDc[',
	    'PoRFlF RSM\\M RbMkM RTQ\\Q RbQjQ RTV\\V RbVkV RR[l[ RZCZI R\\J\\[ RbJb[ RdCdI',
	    'PoSF`F RRJlJ RSNWN R\\NaN RSQWQ R\\QaQ RRUWU R\\UaU R[YbY RYY_Y RUY[Y RSYYY RWJWY RZCZJ R\\J\\X RgDjG RgS_[ RiLeV RlWk[i[ RdCcLeW',
	    'PoZE^E RbQjQ RUT\\T RbTiT RTX\\X RbXjX RR[l[ RZCZH RUQ\\P\\[ RbOb[ RWKZL RWGRK RaFfKlN RVI^I[LSP R`FjFfK_N',
	    'PoRJlJ R\\NaN RSQWQ R\\QaQ RRUWU R\\UaU RWY^Y RRZYZ RSNWMWY R\\L\\Y RVFXH RfXi[ R]F`I RgDjG RfT_[ RVESH RiLeV R]D[H RlWk[i[ RcCcKeX',
	    'PoSFkF RWOZO RbRjR RTU\\U RbUjU RTX\\X RbXjX RR[l[ RTR\\Q\\[ R_J_O R_C_F RbPb[ RcIcN R\\G^I RbG`I RgIjN RWITN RjHcI RhNbN RSI[IZO',
	    'PoTFjF RRMlM RWUhU RWZhZ RWQW[ R_C_F RWQhQh[ RYGZK ReGdK',
	    'Po^ElE RRF]F RRM]M RTU\\U RhWkW RTZ\\Z RTQT[ RTHUL RXCXF R[HZL RTQ\\Q\\Z R_K_W ReEe[ R_KkKkW',
	    'PoRG^G RRM^M R`NhN RTU]U RTZ]Z Rd[j[ RTPT[ RUHUL RXCXG RTP]P]Z R[HZM RcC_L RhS_U RaHlHlSj[',
	    'PoRG^G RRM^M RgNkN RTU]U RTZ]Z RaZkZ RTPT[ RUHUL RXCXG RTP]P]Z RaQa[ RaQkQk[ R_ElElM RdJ_O ReFbM R[HZM',
	    'PoRF]F RRM^M RaNlN RTU\\U RTZ\\Z R`ZlZ RTQT[ RUHUL RXCXF R[HZL RTQ\\Q\\Z R`Q`[ RaFaM RfCfZ RlFlM RlQl[',
	    'PoRF]F RRM]M R^PjP R_TiT RTU\\U R]WkW RTZ\\Z RTQT[ RTHUL RWCXF RZHZL RTQ\\Q\\Z RdNd[ RbE]J R`GeLlO RaGiGdL]O',
	    'PoRF]F R_JjJ RRM]M R_NjN R_QlQ RTU\\U R`VjV RTZ\\Z R^ZlZ RTQT[ RTQ\\Q\\Z R_F_N ReQeZ R_FjFjN RWCXF RTHVK R[HYM RdCdF',
	    'Po^FlF RRF\\F RRM]M R]SlS RTU\\U RTZ\\Z RTQT[ RTHUL RXCXG RZHZL RTQ\\Q\\Z R_L_S RbCbI RgCgI R_LjLjS RfVjZ RdIdSgY RcV`Z][ RdOcV',
	    'PoRF]F R`IjI RRM]M R_OkO R_SkS RTU\\U R_VkV RTZ\\Z RTQT[ RTHUL RXCXF RZHZL RTQ\\Q\\Y R_L_V R`E`I R`EjEjI R_LkLkV RhYkZ RbY][',
	    'PoRF[F RRM[M RaOkO RbRjR RTUZU RaVkV RTZZZ Rc[l[ RTQT[ RTHTL RVCWF RTQZQZZ RfNfY R\\N_M_X[[ ReDaH R\\E^I RcGgKlM R^Xd[ RYHXM RcFjFfKaM R^N\\M',
	    'Po`GkG R`MlM RRN^N RZU^U RbUkU RSW^W RbZkZ RSZ^Z RSQS[ RWNWT R[M[U RSQ^Q^[ RbQb[ RcHcL RfCfG RbQkQk[ RXGYI RTJ\\JZO RXE]G RYCVGRH RiHhM',
	    'PoSE_E RTG^G RhGlG RaIjI RTM^M RVPjP RRSlS RWXhX RWZhZ RWUW[ RYIYM RYCYG RTI^I^M RWUhUh[ RcDhDhG RZQ\\S RePcS RbIeLlM RcDbG RTISO RhK`M',
	    'Po\\GcG R\\IcI R\\McM RUPjP RRSlS RWXhX RWZhZ RWUW[ R\\D\\M R_N_P R\\DcDcI RfDfN RWUhUh[ RSFVI RZQ\\S RaKcM RiHlK ReQbS RWDTG RYFSK RfDlDjH RRLYKVNRO RlMhM',
	    'Po^ElE RSG]G R`KkK R`MkM RRM]M R`OkO R`RlR RTU\\U RTZ\\Z RTPT[ RUHUL RXCXG RTP\\P\\Z RaIaR RcCcG RfHfR RhChG RaI^L RaUdXk[ R\\HZL R_TjTgXcZ^[',
	    'PoSEkE RVMiM RVRiR RVViV RVIVV RVIiIiV R_E^I RcXl[ R[XR[',
	    'Po]ElE RRE[E R^MkM R^RkR R^VkV RRZWZ RWEWZ R^I^V RdEbI R^IkIkV RgXl[ RaXZ[',
	    'Po[ElE R]MjM R]RjR RRVYV R]VjV RSDSV RYRYV R]I]V R]IjIjV RfXl[ RcEbI R_XY[ RXJRM',
	    'Po^EkE R_MjM R_QjQ R_VjV R[W]W RRI[I[V R]S]V R_I_V ReEcI R_IjIjV RiXk[ RgXjY RbX][ RVDVNTV',
	    'Po\\ElE RRG[G R^MjM R^RjR R^VjV RWGWT R^I^V RdEbI R^IjIjV RgXl[ RaXZ[ R\\RRU',
	    'Po_EkE R`MkM R`RkR R`VkV RXFXW R\\D\\Z R`I`V ReEdI R`IkIkV RhXkZ RcX^[ RTDTSR[',
	    'Po^EkE RSE\\E R_MjM RRN]N R_RjR R_VjV RWEW[ R_I_V ReEcI R_IjIjV RgXkZ RbX\\[',
	    'Po\\EkE R^MjM R^RjR R^VjV R^I^V R^IjIjV RfXl[ RdEbI RZKWORP R[SVYRZ RaXZ[ RYDWFRH',
	    'Po^ElE RYL]L R`MkM R`RkR R`VkV RTITX RYCYW R`I`V R`IkIkV RgXlZ ReEcI RcX\\[ R]VRY',
	    'Po]ElE RRE[E RSM[M R^MjM R^RjR R^VjV RWEWV R^I^V R^IjIkV RgXl[ RdEbI R`XZ[ R[URV',
	    'PoTE^E R`ElE RaMkM RaRkR RZR]R RaVkV RXIXY RXI]I]R RaIaV RaIkIkV RiXl[ RcY_[ RfEdI R]WWY RTETSSZ',
	    'Po^ElE R_MjM R_RjR R_VjV R_I_V ReEcI R_IjIkV RgXlZ RbX\\[ RZD\\M RUDSM RWMTX RZR\\WRX',
	    'Po_EkE RSH^H R`MjM R`RjR R`VjV RXDXM R`I`V ReEdI R`IjIjV RiYk[ RUO]X RZSTY RSM]MYU RbY^[',
	    'Po^ElE RTM^M R_MjM R_RjR R_VjV RZMZ[ R_I_V ReEcI R_IjIkV RgYl[ RbX\\[ R\\DTFTSRZ',
	    'Po_EkE RRH^H R`MkM R`RkR R`VkV R[X^X RXCXG RUL[L\\X R^U^X R`I`V ReEdI R`IkIkV RgYlZ RcX][ RULTTSY',
	    'Po^ElE R_MkM R_RkR R_VkV RS[W[ RWMW[ R_I_V ReEcI R_IkIkV RWIZL RRE\\EWJ RTHXJ RgXl[ RRM]M[R RbX\\[',
	    'Po^ElE RSE\\E RRK\\K R_MjM R_RjR R_VjV RZKZW R_I_V ReEcI R_IjIkV RgXlZ R]VXW RbX\\[ RUKUQTX',
	    'Po_ElE R`MkM R`RkR R`VkV RWY[Y RTM\\M\\X R`I`V ReEdI R`IkIkV RgXkZ RbX][ R[D]L RVDTL RWMUX',
	    'Po^ElE R`MkM R`RkR RSS\\S R`VkV RSJSU R\\J\\T RXCXY]X\\Y R`I`V ReEcI R`IkIkV RgXlZ RcX][ R\\FRG',
	    'Po^EkE R_MjM RRO\\O R_QjQ R_VjV RRX\\X RRGRY RWCWX RRG\\G\\Y R_I_V ReEcI R_IjIjV RhXk[ RbX][',
	    'Po^ElE RVI]I R_MkM R_RkR R_VkV RRX\\X RRORZ RVCVO RRO\\O\\X R_I_V RdEdI R_IkIkV RgXlZ RbX\\[',
	    'Po_EkE RSM]M R`MjM R`RjR RRS^S R`VjV RXCX[ R`I`V ReEdI R`IjIjV RgXjY R[E]J RUERK RcY^[',
	    'Po_EkE R`MkM R`RkR R`VkV RYCYM RTG^G]K R`I`V RfEdI R`IkIkV RhXkZ RVP^X RZTUZ RTM]NYW RcX][ RTGTSRZ',
	    'Po^ElE RUJ[J R_MkM R_RkR R_VkV RYW\\W RWNW[ RSN\\N\\W R_I_V ReEcI R_IkIkV RXC]H RVFRJ RgXlZ RWCUH RbX\\[',
	    'Po_ElE R`MkM RXO]O R`RkR R`VkV RTLTY RXJXX R`I`V R`IkIkV RRE]EXJ RgXkZ ReEdI RbX][ R]VRY',
	    'Po_EkE R`MjM R`RjR RSR]R R`VjV RXNXY R`I`V ReEdI R`IjIjV RUGYL^M RgYkZ RSE\\E[HWLRN RbX][ R^XRY',
	    'Po_ElE RSG]G RTK\\K R`MjM RSP]P R`RjR R`VjV RXCX[ R`I`V ReEdI R`IjIjV RWP\\U RVSRW RgXlZ RXOUU RcX^[',
	    'Po_ElE RUK\\K R`MkM R`RkR R`VkV RTX\\X RTPTY RTP\\P\\X R`I`V ReEdI R`IkIkV RXD^I RVGRK RgYlZ RWDVH RbX][',
	    'Po_DlD RRG_G RaMjM RaQjQ RaUjU RaIaU RaIjIjU R\\J^N RhWk[ RUP]W RWCXF RcW_[ RXTRZ RUISN R[NWW RfDdH',
	    'Po_ElE R`MjM R`RjR R`VjV RRGRJ RXCXG RRG]G]J R`I`V ReEdI R`IjIjV RYUTZ RgYlZ RTS[W RbX][ RWITT RRM\\MYV',
	    'Po_EkE RaMkM RaRkR RaVkV RTZ\\Z RTRT[ RTR\\R\\[ RaIaV ReEdI RaIkIkV RhYkZ RVK]R R[KTR RWCSK RVG\\GYM RdX_[',
	    'Po`ElE RTK_K RaMkM RaRkR R[T]T RaVkV RXY^Y RXNXY RXN^N^T R^W^Y RaIaV ReEeI RaIkIkV RhXl[ RcX`[ RWF\\FZJ RWCTKTSSZ',
	    'Po^ElE RRG]G RRM\\M R`MkM R`RkR R`VkV RSX[X RSQSZ RWCWM RSQ\\Q\\X R`I`V ReEcI R`IkIkV RgYlZ RbX\\[',
	    'Po_ElE RRK]K RaMkM RaRkR RaVkV RSY\\Y RSRS[ RXEXQ RSR\\R\\Z RaIaV RfEeI RaIkIkV RhXkZ RcX^[ R\\DRE',
	    'PoRE]E R_ElE R_MkM R_RkR RRR\\R R_VkV RRX^X RRERZ RXEXK RXRXX RRK\\K\\R R_I_V ReEdI R_IkIkV RgXlZ RbX\\[',
	    'PoRE]E R^EkE R`MkM R`RkR R`VkV RRX^X RRERZ RUJUT RXRXX R`I`V ReEdI R`IkIkV RgXlZ RbX\\[ RXEXJ\\K\\RXR',
	    'PoUE^E R`EkE RaMjM RaRjR RUR^R RaVjV RUX^X RRDR[ RUEUZ RZEZK RZRZX RUK^K^R RaIaV ReEdI RaIjIjV RhXl[ RcY^[',
	    'Po_EkE RRG^G R`MjM R`RjR R`VjV R`I`V ReEdI R`IjIjV RiXk[ RSKWP RZU]X RWXRZ R\\RUY R[JZNST RXFUL RbY^[ RXCXI',
	    'Po^ElE RRM[M R_MjM R_RjR R_VjV RRX[X RRGRZ RRG[G[M RRQ[Q[X R_I_V ReEcI R_IjIjV RgXlZ RVCTG RbX\\[',
	    'PoaElE RbMkM RbRkR RbVkV R[[^[ RU[X[ RSEXEX[ RZE^E^[ RbIbV RfEeI RbIkIkV RiXl[ R^OYT RdY`[ RXPRU RTIUN RZI\\M',
	    'Po_ElE RSI^I R`MkM RRP^P R`RkR R`VkV R[G[[ R`I`V R`IkIkV RiYk[ RUDVG R\\CZI RfEdI RcX^Z RVIVRTY',
	    'Po^ElE RSM\\M R_MjM R_RjR R_VjV RXCX[ R_I_V R_IjIjV RhXkZ RWN[R RTFUJ RXLSV R[F[K ReEcI RaX\\Z',
	    'Po_ElE R`MkM R`RkR R`VkV RZY^Y R[D[X R_U_X R`I`V RfEdI R`IkIkV RhYkZ R[O^R RVORS R]H[L RcX][ RSHTL RVDVQUX',
	    'Po_ElE RSH]H R`MkM RRP]P R`RkR R`VkV R`I`V ReEdI R`IkIkV RgYlZ RXCXS]X RVVR[ RTJUN R\\J[N RbX\\[ RXNWW',
	    'Po_ElE RRE]E R`MkM RSO\\O R`RkR R`VkV RSISO RSI\\I\\O R`I`V ReEdI R`IkIkV RgXlZ RbX\\[ RURUV RZQZV R]WRY',
	    'Po_ElE R`MkM RVNXN R`RkR RRT^T R`VkV RVPVX RYCYN R[P[[ R`I`V ReEdI R`IkIkV RiXk[ RYG^L R]FZH RSGVGTL RTZR[ RcX^[',
	    'Po_ElE RWG^G RaMkM RaRkR RRS_S RaVkV RVOVW R\\O\\[ RaIaV ReEeI RaIkIkV RiXl[ RWG[K R^HWN RYDTIYLSO RdX^[',
	    'Po_ElE RRH^H R`MjM R`RjR R`VjV RULVP R`I`V ReEdI R`IjIjV R\\N^P RYCXS^X RWVRZ RgXkZ RbX][ RUJSR R\\JZQ RXMWV',
	    'Po_ElE R`MkM RRP^P R`RkR R`VkV RW[[[ R[M[[ R`I`V R`IkIkV RTSWV RSGUK RXGXK RhXkZ R^F\\K RfEdI RcX^[ R]DRE',
	    'PoaElE RXL`L RbMkM RbQkQ RYU`U RbVkV RYY`Y R]E]U RbIbV RfEeI RbIkIkV RhXl[ RdXa[ RTQVX RREVESM RVMSM R_DXF RWMTX',
	    'Po`ElE RTG^G RRM_M RaMjM RaRjR RaVjV RX[Z[ RYCZM[[ RaIaV ReEeI RaIjIjV RiXl[ RcY_[ R]P^U RTPRV RWMWRUY',
	    'Po_EkE R`MjM RTO]O R`RjR RRR^R R`VjV R`I`V ReEdI R`IjIkV R\\U]X RgYlZ RTURY RTCTK]J]K RbX][ RXRX[U[ R\\ETH',
	    'Po`ElE RRI_I RaMjM RTM]M RaRjR RaVjV RVY]Y RVPV[ RTE]E]M RUR]R]Y RaIaV ReEeI RaIjIjV RiXl[ RWMSU RcY_[ RXEVU',
	    'Po`ElE RTH]H RaMkM RSN^N RaRkR RaVkV RSZ\\Z RSRS[ RYCYN RSR\\R\\[ RaIaV ReEdI RaIkIkV RiYk[ RdY_[ RTDSK',
	    'Po_ElE RUI\\I R`MkM R`RkR R`VkV RTY]Y RTRTZ RTR]R]Y R`I`V ReEdI R`IkIkV RhXkZ RYC]H RXCRI RTL]LZQ RcX^[',
	    'Po_ElE RRE^E R`MkM RRQ^Q R`RkR R`VkV RXQXY R`I`V RfEdI R`IkIkV RhXkZ R[L]O R\\GZK RVKXO RRKTO RTGRK RXGVK RcX][ R^XRY',
	    'Po_ElE RRI^I R`MkM R`RkR R`VkV RYEYP R[Q[X R`I`V RfEdI R`IkIkV RhXkZ R[L]O RXK[L RYIRO R^WYX RcX][ RUQTY R\\DSF',
	    'Po`ElE RRI_I RaMkM RaRkR RaVkV RZ[^[ RYDYO RaIaV RfEeI RaIkIkV RiXk[ RYI^N RcY_Z RYISN RVQUY RSQ[Q[T_T^[ R]DSE',
	    'Po_ElE RYG^G RRL_L R`MkM R`RkR R`VkV RUEUL R`I`V RfEdI R`IkIkV RhXkZ R]N^R RTORS R\\UXYS[ RcX][ RYCYTVT',
	    'Po_ElE RSG]G R`MjM RSQ]Q R`RjR R`VjV RSKSP RXCX[ RSK]K]P R`I`V ReEdI R`IjIjV RWQ]V RVURY RgYlZ RXOUW RbX][',
	    'Po_ElE RRI^I R`MkM R`RkR R`VkV RYEYO RUQ\\Q\\X R`I`V RfEdI R`IkIkV R\\L^N RhXkZ RXI[K RYIRO R_WZX RcX][ RURTY R\\DSE',
	    'Po]ElE R_IjI R_LjL RYU_U Rb[l[ RUSU[ RYQYZ RbQb[ R_GjGjN RlXl[ ReEbG R_G_NhOlQ R\\MTP RZISK RaO\\Q RjSbV RZESG R\\ZS[ R_ZYZ',
	    'Po`ElE RaMkM RaRkR RaVkV RSRSZ RVOVY R[O[Z^X^Z RaIaV RfEdI RaIkIkV RiXl[ R_RZS RcX^[ R\\HSJ R]KSN RVSYSVT R[DSF RYXRZ',
	    'Po`ElE RTI^I RaMjM RTN^N RaRjR RTS^S RaVjV RTX_X RUGU[ RZIZX RaIaV ReEeI RaIjIjV RhXk[ RYDZG RdX^[ RUDTL',
	    'Po_ElE RUK^K R`MkM RYQ^Q R`QkQ R`VkV RTFTJ RTF^F^J R`I`V RfEdI R`IkIkV RhWl[ RXCYF RcX_[ RUSXX RUOTY RZKYY^Y',
	    'PoaElE RSG_G RbMkM RbRkR RbVkV RYDY[ RbIbV RfEeI RbIkIkV RhXkZ R\\M_O RUMWO RYQ^V RWTRY RYOVV RdX_[ RUJSQ R]J[P',
	    'Po_ElE R`MkM R`RkR R`VkV R`I`V ReEeI R`IkIkV RhYlZ RXJ]L RXT]Y RWVS[ R^Q[T RXJTN R]E\\I RUFSI RUQSU RcX^[ RYCWK RYOWX',
	    'Po`ElE RaMjM RaRjR RaVjV RRY^Y RRJRZ R^J^Z RaIaV RfEeI RaIjIjV RhXl[ RYP\\S RVQTT R[KZO RTKUO RdY_[ RSE]EXIXVVV',
	    'PoaElE RTJ^J RbMkM RVQ^Q RbRkR RVU^U RbVkV R[[^[ RTFURV[ RVM^M^Z RTF^F^J RbIbV RbIkIkV RiXl[ RXDZF RdY`[ ReEeI RTPRZ',
	    'Po_EkE RSI]I R`MkM RSN]N RRQ^Q R`RkR R`VkV RSESN RXEX[ RSE]E]N R`I`V RfEdI R`IkIkV RhXkZ R[U^W RWTRY RXT\\U RXPVV RcX][',
	    'Po_ElE RSG^G R`MkM R`RkR RRT^T R`VkV RXCXG RXQX[ R`I`V ReEdI R`IkIkV RiXl[ R[L]O RULWN RUIRP R[JYP RdX^[',
	    'Po^EkE R`MjM R`RjR R`VjV RXTX[ RTCTK\\J\\L R`I`V ReEcI R`IjIjV R[R]U RTPVR RRT\\U\\X RZOUT RWMTP RgXlZ RTVRZ RbX][ R[ESG',
	    'Po_ElE RUJ\\J R`MkM RSO]O R`RkR R`VkV RXJXY R`I`V R`IkIkV RiXl[ RYD]I RbX_[ RYDRJ RURUV R\\R[V ReEdI R]XSZ',
	    'Po`ElE RSJ]J RaMjM RaRjR RSR^R RRU_U RaVjV RSFSR RXRX[ RSF]F]J RSM^M^R RaIaV ReEeI RaIjIjV RiXl[ RbY^[ RXCXF',
	    'Po_ElE RSJ]J R`MkM RSO]O R`RkR R`VkV RW[\\[ RSESO RWSW[ RXEXO R[X\\[ RSE]E]O R`I`V ReEdI R`IkIkV RhXkZ R]S^W RWP[R RcX][ RSTRY',
	    'Po_ElE RTH\\H RTL\\L RaLjL RRO_O RaPjP RaSjS RYS^S RTDTL RTD\\D\\L RaHaS ReEdH RaHjHjS RiVkX RbV^X RURS[ RYOYY RTVYYb[l[',
	    'Po_EkE R`MjM R`RjR R`VjV RUY\\Y RSFSI RURU[ RXCXF RSS\\S\\Y RSF]F]I R`I`V ReEdI R`IjIkV RYN]S RULXN RWIRN RgXkZ RVK[JZNVRRS RcX][',
	    'Po_EkE RSM]M R`MkM RRP^P R`RkR R`VkV RV[[[ RSESJ RYE]E]J R`I`V ReEdI R`IkIkV RgYlZ RbX][ RVERE RYEYJ]J RUQTU\\T[[ RWEWJRJ',
	    'Po_ElE RSF^F RTM_M R`MkM R`RkR R`VkV RYCYG R`I`V ReEdI R`IkIkV RhXkZ RUH]K R\\OVQ R]SVU R]XU[ RcX^[ R[HTK RTMTUR[',
	    'Po`ElE RTI\\I RaMkM RSO]O RaRkR RSS]S RaVkV RRW^W RRERH RSLSS RXGX[ RSL]L]S RRE^E^H RaIaV RaIkIkV RiXl[ RcX`[ RfEdI',
	    'PobElE RTHaH RVK[K RcMkM RcQkQ RVUZU RcVkV RVOVW RVOZOZU RaVaY RcIcV RfEfI RcIkIkV RiXl[ R^D_F ReXb[ R_O[Y R`L]V R]C]M^X RTHTSRZ',
	    'Po_ElE RSI]I RSM]M RaMkM RaRkR RaVkV RTV\\V RZ[][ RRQR[ RSESM RXEXV RSE]E]M RRQ^Q^[ RaIaV ReEdI RaIkIkV RiXl[ RZS\\W RcY_[',
	    'Po`ElE RSJ]J RRM^M RaMkM RaRkR RaVkV RSDSJ RSQS[ RWQWZ RXCXJ RZQZZ R]D]J RSQ^Q^Z RaIaV ReEeI RaIkIkV RiXk[ RcY_[ RXMWT',
	    'Po_EkE RSF^F RTL^L RaMkM RaRkR RaVkV RVHVK RYDYG RaIaV RfEdI RaIkIkV RhXkZ R[H[K R\\NVQ R\\RVU R\\WUZ RcX^[ RTLSURZ',
	    'Po_EkE RSI]I RSM]M R`MjM R`RjR R`VjV RSESM RVOVY RZOZX RSE]E]M R`I`V ReEdI R`IjIjV RSQTV RgYlZ RbX][ R\\P\\U R]WRZ',
	    'Po`ElE RaMkM RSO]O RaRkR RaVkV RV[[[ RSFSO RWSWZ R[X[[ RSF]F]O RaIaV ReEeI RaIkIkV RhXl[ RVI[M RWQZT RcX`[ RZHUN R]T_X RXCWF RTTRY',
	    'Po_ElE RSI^I RRL^L RaMkM RaRkR RTT]T RaVkV RSESI RTOTS RXCXI RTO]O]S R^E^I RaIaV RfEdI RaIkIkV RhYkZ RVUVX RcX][ R[UZYTY R^XWY',
	    'PoTE^E R`ElE RaMkM RWM^M RaRkR RWR^R RaVkV RX[Z[ RWIWR RZEYI R[R[[ RWI^I^R RaIaV ReEeI RaIkIkV RiXl[ RWTVY RdX_[ R^T^X RTETSRZ',
	    'PoaElE RSLXL RbMkM RbRkR RRT`T RbVkV RZQZ[ RbIbV RfEeI RbIkIkV RiXl[ RYTSZ RXMSQ RZL_LYR RVF]I R[N`P RTNXP RZU_X RdY_[ RUD]DZHTJ',
	    'PoaElE RTH_H RVM^M RbMkM RVP^P RbRkR RVS^S RSV_V RbVkV RSKSV RUCUH RVJVS RVJ^J^S RbIbV RfEeI RbIkIkV RiXl[ R[W^Z RVWR[ RdY_[ R]DTE',
	    'Po_ElE RSF^F RTL]L RaMkM RTO]O RaRkR RTS]S RRU_U RaVkV RTITS RYCYI RTI]I]S RaIaV ReEdI RaIkIkV RhXkZ R[X^Z RVXR[ RcX][',
	    'Po`ElE RSF^F RSI^I RRL_L RaMjM RaRjR RTS]S RaVjV RRW_W RZ[][ RTPT[ RVCVL RYLYV R[C[L RTP]P][ RaIaV RaIjIkV RgXkY RfEdI RdX_Z',
	    'Po_ElE R`MjM R`RjR R`VjV RX[[[ RTO[O[[ RTD\\D\\L RTDSLRM]L]N R`I`V R`IjIkV RhWk[ RVRYS RbW_Z RfEdI R[UUW RXHWL R[GWH RTOTUSZ',
	    'Po_ElE RRH^H R`MjM R`RjR RRS^S R`VjV RXDXO R`I`V ReEcI R`IjIjV RTDTG RYV\\Y RXU[X RXVSZ RXHRN R\\D[G RXJ]L RgYlZ RbX][ RXQWW',
	    'PoaElE RbMkM RbRkR RbVkV RZD_D_L RbIbV RbIkIkV RiXl[ RSFUH RZG\\H RdXa[ RYN`Q R^HYK RSDXDXMWORQ RYRTT R[TUW R]WU[ RWHRK RfEeI',
	    'PoRE^E RaElE RSM^M RbMkM RTP]P RbRkR RRS^S RbVkV RU[X[ RSHSM RWEWM RXSX[ RZEZM RSH^H^M RbIbV RfEeI RbIkIkV R\\V\\Y RhXkZ RTVRZ RdX_[',
	    'Po`ElE RSF^F RRL_L RaMkM RaRkR RaVkV R[R[Y RaIaV RaIkIkV RUHVJ RXCYF RhXlZ R]OZS^U R[HZL RfEdI RXLVNRP RYPWRRT RZSWVRX RcX^[ RYZV[ RZP[W',
	    'PoaEkE RRF_F RVJ\\J RbMkM RbRkR RbVkV R\\[_[ RSNS[ RVCVJ RYJY[ R\\C\\J RSN_N_[ RbIbV RfEeI RbIkIkV RiXl[ RdX`[ RVQTW R\\Q\\TZW RVQWW R\\QZP',
	    'PobElE RTGaG RcMkM RTP\\P RcQkQ RcVkV RVZXZ RXHXZ R[R[V RaWaY RcIcV RfEfI RcIkIkV RiXl[ R^D_F ReXb[ R_Q[Z RXK[KXK R`J^V R]C]M^X RTGTSRZ',
	    'Po`EkE RTF]F RRI^I RRMWM RYM^M RaMjM RYP^P RaRjR RRS^S RaVjV RXCXI RZSZZ R[K[P RaIaV ReEeI RaIjIkV RhXkZ R^XYZ RcX^[ RVSTZ RUKVPRP',
	    'Po_ElE RTG]G RTK]K R`MkM RRN_N R`RkR RTT]T R`VkV RU[X[ RTDTJ RYLYN RYTY[ RTD]D]J R`I`V ReEdI R`IkIkV RhYlZ R\\W^Y RUWRY RcX][ RTPTT RTP]P]T',
	    'Po`EkE RUH^H RUK^K RaMjM RUO^O RaRjR RUS_S RaVjV RUGUS RWVWZ RZFZS RaIaV ReEeI RaIjIjV RiXl[ R]U^X RcY_[ RZV[Y RVCRL R[DZI RTVSZ',
	    'PoSE^E RaElE RTL^L RbMkM RWQ_Q RbRkR RWS_S RbVkV RWV_V RWY_Y RWOW[ R[N[Y RTH^H^L RbIbV RfEeI RbIkIkV RiXl[ RWNUT RdY_[ RTHTTRZ',
	    'PoRD^D R`DlD RTJ]J RaJjJ RTM]M RaMjM RTO]O RaPjP RRTlT RTGTO RXDWG RTG]G]O RaGaP ReTe[ ReDdG RaGjGjP RZQ]R RYWVZ RgQkS RWQRS RcQ_S RZTYY',
	    'PoaElE RVI]I RbMkM RbRkR RTRXR R[R_R RbVkV RTMTR RTMXMXR R[L[R R[L_L_R RbIbV RbIkIkV RiXl[ R\\V`Y RUVXX R\\E_H ReXb[ RYD]F RVTSZ R]TZZ RfEeI RZCVHRI',
	    'PobEkE RZF_F RTMaM RcMkM RcQkQ RcVkV RUEUI RZCZI RaWaZ RcIcV RfEfI RcIkIkV RiXl[ RVPZPYVUY R]VZZ ReXb[ RSI`J`K RTMUTUV R_Q\\Y RYVWV R]J]P^Y RTQRZ',
	    'PoaElE RRE_E RVM[M RbMkM RSO^O RbRkR RTT^T RbVkV RTW^W RXZ_Z RUZ[Z RRZYZ RSHSO RTQTW RVJVM RYCYF RVJ[J[M RTQ^Q^W RSH^H_O RbIbV RfEeI RbIkIkV RiXl[ RdY_[',
	    'PoRE^E R`ElE RTKWJ R[M]M RbMkM RRP_P RbRkR RbVkV RRHRK RSSS[ RWSW[ RYEYN RZSZ[ RRH_H_K RbIbV RfEeI RbIkIkV RiXl[ RYPWU RdY`[ RSS^S^[\\[ R[K]JZJ RTMVMSM',
	    'PobEkE RSF`F RTM_M RcMjM RcRjR RcVjV RSPSS RTITM RWCWG RXIXM R[I[N R\\C\\G RTI_I_M RSP`P`S RcIcV RgEdIjIkV RiXl[ RWVZW RYQTV RdXa[ RWT^TZXS[',
	    'PoaElE RTH]H RbMkM RTR^R RbRkR RTU^U RbVkV RTW^W RUOUW RXHXN RRP^O^W RRFRG_F_H RbIbV RfEeI RbIkIkV RiXl[ R[Y_Z RUJSM RdY`[ R]KSP RWYS[',
	    'PoaElE RTH^H RTL^L RbMkM RbRkR RbVkV RWWWZ RTE^E^L RbIbV RfEeI RbIkIkV RiXl[ RYU_T_U R]V_X RYO]QYT RZW[Z RSOVQSUXSXU RTEULTP RTWR[ R\\LZP RdY_[',
	    'Po_DlD RYE]E RRH^H RaIjI RVMYM RVShS RVVhV RRXlX RVOVV RYCYL RbVb[ ReDdF RSPhPhV R_S[V RUJSL R]JZMTP RcN_O RTDUH RfKjK RaFaMiMlO R^P^V RdKaKeK RaFjFjLfK',
	    'PoaElE RYE_E RVK\\K RYM^M RbMkM RVQ_Q RbRkR RbVkV RVZ\\Z RVVVZ RYHYM RYVYZ RYCYG R\\V\\Z RTH`H_J RbIbV RfEeI RbIkIkV RiXl[ RdY`[ RZNZS^S RVNVSZSWS RVN_N^S[S RTGTTRZ RVV_V_YXZ',
	    'Po`ElE RaMkM RRQ_Q RaRkR RaVkV RVW[W RSZ^Z RSSS[ RVUVV[U[X RSS^S^[ RaIaV RfEdI RaIkIkV RXNXQ RcY_Z RUDSE RUJSK R]D\\E RXF^H RXK^N RgYl[ RXIWLSN RXCWGSH',
	    'PoaElE RUG]G RUJ]J RSM_M RbMkM RbRkR RSRXR RZR_R RZU_U RbVkV RSOSY RUEUM RROXOX[ RZOZY RSE^E]N RYO_O_[ RbIbV RfEeI RbIkIkV RiXl[ RdXa[ RWXRY R`XYY RSUXUSU',
	    'PoaElE RSE_E RSMXM RZM_M RbMkM RTQ_Q RbRkR RUS^S RbVkV RUV^V RUY_Y RSISM RUOU[ RVCVG RSIXIXM RZOZY RZIZM R\\C\\G RZI_I_M RbIbV RfEeI RbIkIkV RiXl[ RUPRS RdY`[',
	    'PoRElE RVJVV RVJhJhV RbVk[ R_E]J R_M_U[XR[',
	    'Po^ElE RRE\\E RRZWZ RWEWZ R_I_U R_IkIkU RhXk[ ReEcI RdVbY][ ReLeRdW',
	    'Po\\ElE RSKZK RSDSW R]J]U R]JjJjU ReWlZ RdEbI RdMcU`YZ[ RZTRV',
	    'Po_ElE RSE\\E RRN]N RWEW[ R`I`U R`IkIkU RhWl[ RfEdI ReVbY^[ RfLfSeW',
	    'Po\\ElE RRG[G RWGWT R]I]U RdLdU R]IjIjU RiXl[ RfWjY RdEbI RcU`Y[[ R[SRU',
	    'Po_ElE RXFXW R\\D\\Z RaIaU ReLeV RaIkIkU RgVl[ RfEdI ReVbZ_[ RTDTTR[',
	    'Po\\EkE R]I]U R]IjIjU ReWjY RYDTI RcEbI RYKVORP RZSVXRZ RdLcV`Y[[',
	    'Po]ElE RREZE RSMZM RWEWW R^J^U R^JkJkU RiXl[ RfWjY ReEcI ReLdVaY][ RZURX',
	    'Po_ElE RSE\\E RRK^K RZKZX R`I`U R`IkIkU RhXl[ RfEdI R^VYX RdVaY][ RVKUQTX RfLfQdW',
	    'PoTE^E R`ElE RZS]S RWIWZ RWI]I]R RaJaU RaJkJkU RhXl[ ReVa[ RfEeI R]XWY RTETTR[ RfMfSeW',
	    'Po_ElE RRG]G RSR]R RSJSR RXCXZ R]J]R RaIaU RaIkIkU RhXl[ RfEdI R]WWZ RdVaZ^[ RfMdW',
	    'Po_ElE RTL]L RZLZZ R`I`U R`IjIjU RhXl[ ReLeV`Z RfEdI R\\DTFTRR[',
	    'Po_ElE RW[[[ RaIaU RfLfU RaIkIkU RgWl[ RZD^L RWDRM RfEdI ReVbY_[ RWMUX RTM\\M\\V[Z',
	    'Po_ElE R`I`U R`IkIkU RhWl[ RVERM RWNSX RfEdI ReVbZ_[ RZD\\M RfLeSeW R[Q\\XRX',
	    'Po`ElE RRH^H RUL[L[Y RaIaU RaIjIjU RhWl[ RdW`Z RWCXG RfEdI R^WZX RULUSTY RfLfQdW',
	    'Po_ElE RS[W[ RWMW[ R`I`U ReLeU R`IkIkU RgWk[ RWJ[M RRE\\EWJ RTIWJ RRN]N[R RfEdI RdVbY][',
	    'Po`EkE RYF^F RTQ]Q RYCYK RTK^K^S RaJaU RaJjJjU RhXl[ RfEdI RfMeVbY^[ RTKTTS[',
	    'Po`ElE RaIaU RaIjIkU RiXl[ RWKYM RUUZ[ RXE^K RVGRL RXCUI RSP\\PYV RfEdI RdWbY^[ RfLeQdW',
	    'Po`ElE RYCYM RaIaU RaIkIkU RhWl[ RVQ^X R[TUZ RTG^G]K RTM^NYW RfEdI ReWbZ^[ RTGTTR[ RfLfQeW',
	    'Po_ElE RSQ]Q RXQXY R`I`U ReLeU R`IkIkU RgWk[ RXJ^M RfEdI ReVbY^[ RSE\\EVLRM R]XRY',
	    'Po_ElE RRG]G RRM]M RSY\\Y RSQS[ RXCXM RSQ\\Q\\X RaIaU RaIkIkU RiXl[ RgXjY RfEdI RdWbZ^[ RfLfQdW',
	    'Po_ElE RRH^H RRP^P RaIaU RaIkIkU RiXl[ RXCXS]X RVVR[ RTJUO R\\J[O RfEdI RdWbZ^[ RXMWV RfLfQdW',
	    'PocElE RZMaM RZUbU RZZbZ R^E^U RdIdU RfEfI RdIkIkU RUUYY RhWl[ RfXb[ RTQVW RSEXETM RWRTZ RaDZF RSMXMVW RgLgRfX',
	    'Po_ElE RTL[L RTY\\Y RTPTZ RTP\\P\\Y R`I`U R`IkIkU RhWk[ RXD]I RWGRK RXCUI RfEdI ReLeVbY][',
	    'Po_ElE RTL]L RU[X[ RUCUL RYMY[ R]I]L RaJaU RaJjJjU RhWk[ R^P[S]W RRQVQSX ReEcI RfLeVbY^[ R\\ETH',
	    'Po_ElE RTL\\L RTCTL RaJaU RaJjJjU RhXl[ RXU\\W R]Q[S RWWT[ R]I\\L RTPSU ReEdI ReMeVbY^[ R\\ETG RXOWX',
	    'Po`ElE RRG^G RSNZN RaIaU ReMeV RaIkIkU RiXk[ R[T^X RWXRZ R]RUY ReV`Z RWISM R[LST RXDXGVL RfEdI',
	    'PoRE]E R`ElE RVR\\R RRY]Y RRERY RVKVR RYEYK RYRYY RVK\\K\\R RaIaT ReMeU RaIkIkT ReV`[ RgXkY RfEdI',
	    'Po`ElE RYG^G RRL^L RUFUL RYCYT RaIaU RaIkIkU RhXl[ RUOST RfEdI R]P[UWYS[ RdWbY^[ RfLfQdW',
	    'Po_ElE RVOYO RRT_T RYCYN R[P[[ RaJaU ReMeU RaJjJjU RhXl[ R]F[H]M RSGWGTL ReEdI RTZRZ RdVbY^[ RVQUY',
	    'Po`ElE RRI_I RYEYO RUQ\\Q\\Y RaIaU RaIkIkU RgWl[ R[K^M RYIRO RfEeI R_W[Y RdWbZ^[ RURTY RfMeW R]DTE',
	    'Po`ElE RTY]Y RTRTZ RTR]R]Z RaIaU RaIjIjU RhXl[ RXHZJ RZE^I RdWa[ RYCRJ RSM]MYQ RfEdI RfLfRdX',
	    'Po_ElE RTO]O RRR^R RU[X[ RXRX[ RaJaU RaJjJjU RiXl[ R\\U]X RTUSY ReEdI RTCTK]J]K ReMeVbY^[ R\\ETG',
	    'Po_ElE RRR^R RYNY[ RTCTK]J]K RaJaU RaJjJjU RhWk[ RXR]W RYRRY ReEcJ ReLeVbY^[ R\\ETH R]MSO',
	    'Po`ElE RTI^I RTM^M RRQ^Q RTETM RXEX[ RTE^E^M RaIaT RfMfU RaIkIkT RhXl[ RXQ]V ReVaZ RYQRX RfEdI',
	    'Po`ElE RTH]H RTL]L RRO_O RYT^T R\\[l[ RTDTL RTD]D]L RaIaS RaIjIjS RUUYY RhUkX RdTaX ReEeI RURSZ RYOYYb[ RfKdU',
	    'Po`ElE RSI]I RSM]M RTV\\V RZ[^[ RRQR[ RSESM RXEXV RSE]E]M RRQ^Q^[ RaIaU RaIkIkU RhXk[ ReWaZ R[S\\W RfEdI RfLfQdW',
	    'Po`ElE RSJWJ RZJ^J RSM]M RRP_P RW[\\[ RSDSJ RSDWDWJ RZDZJ RZD^D^J RaIaT RaIkIkT RhWl[ ReV`[ RfEdI RVPUT]T\\[ RfMfSeW',
	    'Po`ElE RSJ^J RRM_M RSDSJ RSQS[ RWQWZ RXDXJ RZQZZ R^D^J RaIaU RaIkIkU RhWk[ ReWa[ RfEeI RXMWT RfMeW RSQ^Q^Z\\Z',
	    'Po`ElE RSF^F RTK^K RaIaU RaIkIkU RhWl[ RVGVJ RXCYF R\\G[K RfEdI R]MUP R\\RUU R]U[XUZ ReWbY^[ RTKTUR[ RfLfQeW',
	    'Po`ElE RUZ\\Z RUSU[ RUS\\S\\[ RSFSG^F^I R`I`U R`IjIjU RhXl[ RXCYF RdW`[ RVJRN RUN^R RfEdI RVK\\KWPRR ReLeQdX',
	    'PoaElE RVH]H RVK]K RWN`N RSN]N RVEVN RSE^E]O RbIbU RbIjIjU RiXl[ R[S_X RSSXX RWVRZ R]VYZ RSQXQUY RZQ_Q\\Y RfEeI RfLfVcZ`[',
	    'PobElE RRF`F RRJ_J RZ[^[ RSNS[ RVCVH R\\C\\H RSN_N_[ RcIcU RcIkIkU RhXl[ RgMgWc[ R[J[R]V RgEfI RWJWPVV R[NZW',
	    'Po`ElE RRF^F RTM]M RTP]P RTS]S RRV_V RTITV RTI]I]V RaIaU RaIkIkU RhWk[ R[W]Z RVWR[ ReWa[ RfEeI RYCXJ RfLfQeW',
	    'PobElE RSLXL RRT`T RYRY[ RbIbU RbIkIkU RhWl[ ReWb[ RWMSQ RZL_LZQ RYSSZ RVF]I RZN`P RSNXP RYU^W RfEeI RTD]DZGTJ RgLgReX',
	    'Po`ElE RSG]G RSJ]J RRN^N RTT]T RV[X[ RSDSJ RXTX[ RTP]P]T RSD]D]J RaIaU RaIkIkU RhWl[ R\\V]Y RTVSZ RfEeI ReWbZ_[ RTQTT RfLfQeX',
	    'Po`ElE RRH_H RSQYQ RST^T RXDXM RaIaU RaIjIjU RiXl[ RTDUF R\\R^T R\\V]Y R]D[F RWNSQ RUWRZ RXI^L RfEdI R[OXRST RdWbZ_[ RXHUKRL RYTY[V[ RfLeRdX',
	    'Po`ElE RRF^F RVM[M RSO^O RTT]T RTW]W RSHSO RTQTW RVKVL[K[M RTQ]Q]W RSH^H^O RaIaT RfMfU RaIkIkT RXCYF ReVa[ RgXkY RfEdI RYZRZ R\\YUZ R^YXZ',
	    'PoSE_E RaElE RTKWK R[K]K RTMWM R[M^M RRP_P RRHRK RSTS[ RWTW[ RYEYN RZTZ[ RRH_H_K RbIbU RbIkIkU RhWl[ ReWa[ RYPWU RfEeI RfLfQeX RST^T^[\\[',
	    'Po`DlD RYE^E RRH_H RVSiS RVViV RRXlX RVPVU RaVa[ RaGaL ReDdG RVPiPiU RaGjGjL RUJTM RfMlO R]KVN RfIdM`O R_P]UVX RUEUI RYCZMRO',
	    'PoaElE RRE_E RTLXL RZL_L RUQ_Q RUS^S RUV^V RUY_Y RTITL RUOU[ RVCVG RTIXIXL R[O[Y R\\C\\G RZI_I_L RbIbU RfMfU RbIkIkU RhWlZ ReVa[ RUORS RfEeI RZIZL',
	    'PoYSeS R[YfY RXYcY RYMYS R_I_X RYMeMeS RcUfX RlWl[ RVEiEiTj[ RVEVQTY RdH[I',
	    'PoVS]S RVLVS R[H[X RVL]L]S RhEdI RiJgMcO RkPhScU R]GVH RaWeZj[ RTE`E`NaW RTETTR[ R]U^YUY',
	    'PoSDkD R[ZeZ RWZbZ R_D_J RXO_O_Y RYQeQeV RYQYVdVg[ RlXk[l[ R_FiI RVLhLhTiZ RVLVSTY RdN]N',
	    'PobHkH RaMlM RVS]S RVLVS RZHZX RVL]L]S RfCfOjVlV R]GVH RfObVdZj[ RTD_D_MaW RTDTTR[ RfJgOeT R\\U^XUY',
	    'PobMlM RVS\\S ReViV RWX^X RUY[Y RVLVS RZHZX RVL\\L\\S RbHbL RbEjEiV RhMaT RkXk[ R\\U^Z R\\GVH RTD_D_O`WdZj[ RTDTTR[',
	    'PofHlH RVS]S RcVkV RVLVS RZHZX RVL]L]S RcNcU RfCfN RcNkNkU R\\GVH RaVdZj[ RTD_D_NaW RTDTSR[ R\\U^XUY',
	    'PobNgN RVS\\S RbUkU RVY]Y RVLVS RZHZX RVL\\L\\S RbIbT RbIgIgN RbGkFkP RkRkU RkXl[ R\\U^Z RcC`J R\\GVH RjQgQ RTD^D^O`WcZj[ RTDTSR[',
	    'PoRI[I R`SfS RWCWI R`L`S RcHcY R`LfLfS RlWl[ RfG_H R\\URW RTLUT R]DiDiUj[ RYKXV R]D]R[X RfUgY^Y',
	    'PoaHlH RVS\\S RVLVS RYHYX RVL\\L\\S RgRkV RhDjF RgRcW RkXl[ ReCdKhT RiLeU RdHgR R[GUH RTD^D^N`WcZj[ RTDTTR[ RdFbR R\\U]YUY',
	    'PoaKkK RVS]S RcVjV RVLVS RZHZX RVL]L]S RcNjNjV RjHkL R]U_Z RlWk[ ReCcK R]GVH RcNbVdZj[ RTD_D_NaW RTDTSR[ R^XUY',
	    'Po`EgE RbJfJ RVS\\S RhWkW Rd[k[ RVLVS RZHZX RVL\\L\\S RhGhQ RkDkV RfKcU R\\GUH RaLeO`WcZi[ RbE`O RTD^D^P_W RTDTTR[ R\\U]YVX',
	    'PobJlJ RVS]S RcVkV RWX^X RVLVS RZHZX RVL]L]S RcOcU RgEgO RcOkOkU RlXk[ R]U_Z R\\GVH RjDbE RTD_D_MaVeZj[ RTDTTR[',
	    'PobGlG RfPkP RVS\\S RVLVS RYHYX RVL\\L\\S RaJkJiM RcC`J R\\GUH RTD^D^N`W RfJfVkW RbNbQ`VcZj[ RTDTTR[ R\\U]YVX',
	    'PobNkN RbRkR RVS\\S RhXjX RXX^X RUY[Y RVLVS RZHZX RVL\\L]S RbJbW RfCfJ RbJkJkW RkXl[ R\\U^Z RbEcH RjEiI R\\GVH RTD_D_P`X RbZj[ RTDTTR[',
	    'PocHjH RcKjK RcNjN RaQlQ RVS\\S RWX^X RVLVS RVL\\L\\S RcEcQ RcEjEjQ RiSkV RdSbU RkXk[ R\\U^Z R\\GVH RaWdZj[ RTD_D_N`W RTDTTR[ RZHZXUX',
	    'PoVS\\S RVLVS RYHYX RVL\\L\\S RgSkV RkEiH RbNaR RkOiQ RkXl[ RbEaH ReIjK RdK`L R[GUH RfCeJ RfLeT RTD^D^N`W RcZj[ RTDTTR[ R\\U]YUY',
	    'PoaIjI RaMjM RVS\\S RdViV RWY^Y RVLVS RZHZX RVL\\L\\S RaEaM RdPdV RfEfM RaEjEjM ReNgP RjQkT RlXk[ R\\U^Z R\\GVH RTD_D_N`WdZj[ RTDTTR[',
	    'PoaIlI RbOkO RaQlQ RVS\\S RbUlU Rd[k[ RVLVS RYHYX RVL\\L\\S RbKbO RcQbU RgOgX RbFjFjI RbKkKkO ReCdI R\\GUH RTD^D^Q_X RaZi[ RTDTTR[ R\\U]YVX',
	    'PoaHkH RaKkK R`MlM RVS\\S RUY\\Y RVLVS RZHZX RVL\\L\\S RaEaJ RaEkEkJ RcN_R RlXk[ R\\U^Z RhPeW R\\GVH RjWgW RbPkPjW RTD_D_N`W RdQ`VdZj[ RTDTTR[',
	    'PoUO]O RRS^S RbSgS RTY\\Y RTUTZ RXNXY R\\U\\Z RbLbS RbLgLhS RTHWK RVF]FXK RULSQ RVFZJRM RXCVGRH RgUhZ RhGaH R_EjEkXl[ ReGfY`Y R_E_S^[',
	    'PohIkI RaLdL RaOjO RVS\\S Rd[k[ RVLVS RYHYX RVL\\L\\S RaEaL RfCfO RcQgUkW RdD`E R[GUH RjQfU`WcZi[ RaHdH`I RhEkEkLgL RTD^D^P_W RTDTTR[ R\\U]YUY',
	    'PoSG\\G RRK\\K RaSgS RST[T RSDSH RSOSS RWCWG RSO[O[S R\\D\\H RaLaR RdHdY RaLgLgR RTVVZ RlWl[ RfG`H R_DiDjUj[ R_D_R]Z RZUZYRZ RgVhY`Y',
	    'PoaRjR RVS\\S RdVkV RaViV RVLVS RYHYX RVL\\L\\S RaNaR RaNjNjR RiTlW ReFgF RaDeIlK R`DkDgH`K R[GUH Rk[g[ RTD^D^P_WcZi[ RTDTTR[ R\\U]YUY RfKfV_W',
	    'PoTE\\E RTI[I RTM[M RTQ\\Q R`SgS RTETQ RXEXQ R`L`S RdHdX R`LgLgS RYSZV RWTWW RlWl[ R[[X[ RUTUX RSTRY R^DiDiUj[ RgG_H R^D^T\\[ RfUgY_Y',
	    'PoaRjR RVS\\S RaVjV Rd[k[ RVLVS RZHZX RVL\\L\\S R`E`K RfNfV RaNjNjW RdGdK_K ReD`D R\\GUH RkKiL RTD^D^P_X RaN`WbZi[ RhEgL RfElEkL RTDTUR[ R\\U]YUY',
	    'PobShS RTU]U RTY]Y RTQTZ RTQ]Q]Z RYD]D^N RbLbS RbLhLhS RTFTI RYG[H R`EjEkYl[ RRDWDWNXQ R]JYL RWJRL RgUhZ RhGaH ReGgXaY R`E`T_[',
	    'PoVS\\S RVLVS RYHYX RVL\\L\\S R`DeDeK RgDkDkK RiEfH RjHgK RcE`H RdH`K RgMlO RdN`O RgObQ RhRcT RjTcW R[GUH RTD^D^N`WcZj[ RTDTTR[ R\\U]YVX',
	    'Po`DlD RaLkL RbOjO RaQlQ RVS\\S RUY\\Y RVLVS RZHZX RVL\\L\\S RaHaL RdDdL RhDhL RaHkHkL RiSlV RbSbV RlXk[ R\\U^Z R\\GVH RfQfWcW RTD_D_N`WdZj[ RTDTTR[',
	    'PoRE]E RSM]M RTP\\P RaShS RRS]S RU[X[ RSHSM RVEVM RXSX[ RYEYM RSH]H]M RaLaS RdGdY RaLhLhS R\\V]Y RTVRZ R_DjDkXl[ RhG`H R_D_S]Z RgUhZ_Y',
	    'PoaDlD RbRkR RVS\\S RbVkV RVLVS RYHYX RVL\\L\\S ReDeNfV RhDhN RbNkNkW RaGaKeK R\\GUH RdGaG RhGkGkKgK RTD^D^N`W RbNaWcZj[ RTDTTR[ R\\U]YVX',
	    'PoRG]G RRRWR RXR]R RbShS RbLbS RbLhLhS RYI\\M RZNZV\\Y RZD\\E RVJTNUVVY RWFYK R\\O]Q RhGaH R_EjEkXl[ RWCVK RTPSZ RZPYY R_E_S^Z ReGfWhY`Y',
	    'PoaFkF RfOlO RVS\\S RVLVS RYHYX RVL\\L\\S ReEgHiJ RhDiE R`OcOdU RdJaK RiMkV RbO`W R\\GUH ReCdI RTD^D^P_W RbZj[ RTDTTR[ RbMaV RhLgU R\\U]YVX',
	    'PoVS\\S RUY\\Y RVLVS RZHZX RVL\\L\\S RcReT RhRlU ReHhJkL RkEiG RlXk[ RbEbH R\\U^Z RfOfQ RkOkQ RTD_D`PaQ RdJ`L R\\GVH RfCeJ RiMhT R^L`WdZj[ RTDTTR[ RcMbU',
	    'Po`FlF RbNjN RbQjQ RVS\\S RcXfX RVLVS RYHYX RVL\\L\\S RbJbQ RfQfX RaKkKjQ RaHbI RfFlL RkHjJ RbSaV RfF`L RjSkV RfCdI RkZg[ R[GUH RTD^D^P_W RaZi[ RTDTUR[ R\\U]YUY',
	    'PobSgS RRPRS RYPYR RbLbS RbLgLgS RVSWW RWH\\J R`DjDkWl[ RTETH R\\E[H R]O]R RVJSL RhUhZ RXDWJ RgGbH ReHfXaY RjTjZ RUNSY R[NZX R`D`S^Z',
	    'Po`HeH RgHkH RfKlK RVS\\S RdWhW Rd[k[ RVLVS RYHYX RVL\\L\\S RdOdW RfDjDiK RhThW RdMfO RiNgQaV RjPlT R`DdDdK`K RaPaU R[GUH RTD^D^P`WcZi[ RTDTTR[ R\\U]YVX',
	    'PoZF_F RSMWM RZM^M RTQ^Q RcShS RTT]T RTV]V RTY^Y RSISM RUPU[ RRFWDWG RSIWIWM RYOYY RZIZM RZCZG RcLcS RcLhLhS R`EjEkZ RUNRT RgUhZ RZI^I^M RhGaH RfGgYaY R`E`T_[',
	    'PoZNdN RWNbN RUW[W RcXhX RTZ[Z Rb[i[ RSPSY RUUUW RXRXZ RUUZUZW RSP]P]Y R^F^N RcUcX RVDWG]HdIdK RfRfZ RcUhUhX RaPjPkZ RXIXKcKfO RZRUR RgRbR RVDhDhIhN RVFUM RaPaV_[ RdF^F RkLkOiN',
	    'Po]OcT RYJbSfW R_QXX RlWl[ RcH^R RVEiEiTiZ RVEVQTY',
	    'PoaKkK ReWjW Rd[k[ ReHbK R`EiEcJ RfRbV RVJ\\W RhLeT RZNVX RdKbR R[HWU RTD^D^P_WcZi[ RkMjV RTDTTR[',
	    'PofHlH RbUkU RbMbU RfCfM RbMkMkU RXK\\V RZMVW R[HXT RTD^D^N_WcZj[ RTDTSR[',
	    'PobNgN RbUkU RbIbU RbIgIgN RbGkFkP RkRkU RVI\\V RcC`J RjQgQ RkXk[g[ R[HWV RTD^D^Q_X RbZi[ RTDTTR[',
	    'PoRI[I R^EiEkXl[ RVDWG R`IgW ReL`X RfHbU RTKUU RiSj[ RZKWW R[VRX R^E^R\\Y',
	    'PobHjH RbKjK RbOjO R`RlR Rd[k[ RbEbR RbEjEjR RhTjV RcTbW RlXk[ RVIZQ\\X R[HWW RTD^D^O_WbZi[ RTDTTR[',
	    'PoaIjI RaMjM RaEaM RfEfM RdPcWiUiW RaEjEjM ReOgQ RVI\\V RjPlT RYOUW R[IWU RkXk[g[ RTE^E^O_W R`Q_VbZi[ RTETTR[',
	    'Po`HdH RgHkH R`LkL Rd[k[ RaEaL RfCfO RgEkEkL RVI\\V RaRhU RYNUW RaPjPfUaW RdD`E RZIWV RTE]E^P_WbZi[ RTETSR[',
	    'PoTN]N RRS]S RTZ\\Z RTUTZ RXNXZ R\\U\\[ R`EjEjY RXGXK RbJhX RfMaX RULRQ R\\F[K RTGTK RgHcU R`E`S^[ R\\DRF',
	    'PoaRjR RaVjV Rc[k[ R`E`K RaNaW ReNeV RaNjNjW RlXk[ RVJ[V RdGdK_K ReD`E RZIVV RkKiL RhEgL RTD]D]Q^X RaZi[ RfElEkL RTDTTR[',
	    'PoRE^E RSM]M RTP\\P RRS^S RU[X[ RSHSM RVEVM RXSX[ RZEZM RSH]H]M R`EjEkYl[ RfNaX RTVRZ R[V\\Y RbJfQgX RgIcV RjTj[ R`E`S_Z',
	    'PoSG]G RYQ^Q R\\S]W RUNUUXV RZD\\E RXHTL RWNWO RaJgW RWH\\J RePaY RlWl[ RXDWJ RfKbW R`DjDjVj[ RfHdU RRQVQTY R[NYX R`D`S^[',
	    'PocZkZ RkWkZ RcReT RhRlU ReHhJjL RjEiG RbEaH RXL\\U RfOfQ RkOkQ RVKYOVV RaOaQ RdJ`L R[IXT RfCeJ RiMhT RTD_D_Q_X RaYiZ RTDTTR[ RcMbU',
	    'Po^G^[ RlXl[ RjDhG RjQhS RfUkW RfYk[ RfHkJ RgMkNkO RZHRK RUEdEgM RSPePfY RWIWQT[',
	    'Po_DhD RRJ]J RTU\\U RTY\\Y RTQT[ RYDYP RXQXY RTQ]Q][ ReGe[ RlMlO RlYl[ RZL]O RXK[M RhSlV RhIlJ RkFjI RkQjT RYJRP RTFUI R[F[I RhFiN R^PiPiUiZ RaG`K`S_Y R\\DSE',
	    'PoZR^R R_PgQkR RRUYV]X RWRW[ R^F^N RdRd[ RWKTN RSPYP[U RgPhTkU RgWkX R_VfVhZk[ RYUZZ][ RTEeEfHjJ ReFjG RjEeF RSJdJeLjL RfNkO RWGWM RVRSRTZ RcR`S`Z R[FTG',
	    'PofKcN RjHfL RREcEcOjQ RfZj[ RlVk[ RcKeX',
	    'PoWOgO RWSgS RU[]Z RWKW[ R_G_K RWKgKgS R_CdHlK RjSbW R_CYIRL R^SbXfZk[',
	    'PoVObO RVSbS RVKVZ R[G[K RVKaKaS R^Ud[ R[C_EcI R[CTJ RaXS[',
	    'Po_HfH R]OhO R]ShS R]L]Z R]LhLhS RSEWI RbTfYk[ RcChHlJ RWORX RbC]IYJ RjTdW RdYZ[',
	    'PoUN]N RUR]R Rh[l[ RUJUY RYGYJ RUJ]J]R RaDiDiZ RlVl[ RYC^H RXDRJ R[U\\XRY RaDaR_X',
	    'Po_FlF RVH\\H RUN]N RUR]R RUU]U RUX]X Rb[f[ RUKU[ RUK]K]Q RfFf[ RYC]H RYCRJ',
	    'PoUO]O RUS]S RUKUZ RXHYK RUK]K]S RYD\\F^H RYCRJ RfPjZ RdQ_[ ReCeLhW ReLaX R[U]XSZ ReHfS RdIcU',
	    'PoVH[H RUN\\N RUR\\R RUU]U RUX]X Rd[k[ RUKU[ RUK\\K\\R RdFd[ RXC]H RlWl[ RXCRJ RjD_F RlN_P',
	    'Po`EkE RVH\\H RUN]N R_NlN RUR]R RUU]U RUX]X RUKU[ RUK]K]Q ReEe[ RYC]H RYCRJ',
	    'PoVH\\H RaHgH R_OiO R_SiS R_L_Z R_LiLiS RdCkJ RWVS[ RjUeW RcUgYl[ RTMWNZPVY RWCSP RdC\\JXV ReY\\[',
	    'Po^OiO R^SiS R^K^[ R^KiKiS RhXkZ RcHdK RcDiJlK RXVS[ RWNSQ RcD\\K RcUhX RWCSL RYHWN RZOWX RjUfW RdY]Z RZQSR RXKRK',
	    'PoVH[H RUN\\N RUR\\R R_TkT RUU]U RUX]X Re[l[ RUKU[ RUK\\K\\R R_K_V RfCf[ RkKkU RlXl[ RXC]H RXCRJ RkF_H',
	    'PoVH\\H RUN]N R^OlO RUR]R RUU]U RUX]X R_ZkZ RUKU[ RUK]K]Q RfEfZ RYC]H RYCRJ RjD_F',
	    'PoVH[H R^NkN RUN\\N RUR\\R RUU\\U RUX]X RUKU[ RUK\\K\\Q RhWl[ RXC]H RbV][ RXDRJ RdEeRiY RdQaX RdMhW ReNbW RjD^F RdJfT',
	    'PoUHiH RSKlK RXRfR RXUfU RXOX[ RXDYF RfDeF RcKfNlQ R_C]JXORQ RXOgOhUdX R_WkZ R_YVZ',
	    'PoaHlH RVH[H RUN\\N RUR\\R RUU]U RUX]X Rf[j[ RUKU[ RUK]K]R RYC]H RcV^[ RXCRJ RdRaY RbC^K R`NlNkUj[ ReJcV',
	    'PoVH[H RUN\\N RUR\\R RUU]U RUX]X RUKU[ RUK\\K\\Q RgVl[ RXC]H RbV][ RXDRJ ReHeQhY RdQaX R`IkIjO RbC^N ReLePcU',
	    'Po`EkE R`LkL RUN\\N RUR\\R RUJUY RYGYJ RUJ\\J\\R RYC]H RXDRJ RZT\\XSY R`E`R^Y RaMfVk[ RkMgVaZ',
	    'PoVH\\H R^IlI RUN]N RUR]R RUU]U RUX]X RUKU[ RUK]K]Q RdCdI RdKd[ RYC]H RYCRJ RdOkR',
	    'PoRD\\D RaHgH R^OiO R^SiS R^L^Z R^LiLiS RdCkJ RXVS[ RdC]J RcUgYl[ RSOYR RZOVY RjUeW RVDSP RUK\\KXV ReY\\[',
	    'PoUN]N RUR]R RUJUY RYGYJ RUJ]J]R RYC]H RXDRJ RaIkHiN RbC_N R[T\\XSY ReKePdSbW^[ RePhWl[',
	    'PoVH\\H R_IlI RUN]N R_QkQ RUR]R RUU]U RUX]X R^ZlZ RUKU[ RUK]K]Q ReCeH ReJeZ RYC]H RYCRJ',
	    'PoVH[H R`MiM R^MgM RUN\\N RUR\\R RUU\\U RUX]X R`ZjZ RUKU[ RUK\\K\\Q R`Q`[ R`QkQk[ RXC]H RXDRJ RiHkN RcCaM',
	    'PoaIlI ReNlN RTO]O RTS]S ReTlT RTKTZ RXHYK RTK]K]S RfHf[ R[E^H RYCSI RXE\\F RcC_M R[U\\YSZ',
	    'PoVH[H RUN\\N RUR\\R RUU]U RUX]X Rc[j[ RUKU[ RUK\\K\\R RcIc[ RjWj[ RXC]H RdT][ RXCRJ RaDgF RjGdT RjMlT R_M^U',
	    'PoaLjL RTO\\O RTS\\S RaSjS R^[l[ RTKT[ RXHYK RTK\\K\\S RaEa[ RaEjEj[ RZE]I RYCRJ R[U\\YS[',
	    'PoSK[K RRN\\N R_OiO R_SiS RV[Y[ RSESJ RSE[E[J R_L_Z RcHdK R_LiLiS RjUgW RcD]K RcUhW RiYkZ RdDhIlJ RdY^Z RUNTS[SZ[',
	    'PoTO[O RTS[S RhVlV RTKTZ RWHYK RTK[K[S R^F^U RgFg[ RgFlFlU RXD\\H RbW][ RXCRI RZU[YSZ RdD^E RcGcT]U RcObW',
	    'PoYF_F RaKkK RXSfS RXVfV RTFTK RXOX[ RYDYJ RbDbJ R^N`P RkHkK R_LUPgPhVdX R_LdNlP RjEaG R\\NRQ R_WkZ R_YW[ R\\JRK R_JTK',
	    'PoVH[H RTN\\N RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q RXC]H RfFlK ReCgG ReJ`P RWDRI RcG]L RdCbI RhOdS`T RkSfX_[',
	    'Po`IhI RUN]N RUR]R R`TgT Rf[l[ RUJUY RYGYJ RUJ]J]R R`M`W R`MgMgT R_ElEl[ RYC^H RXDRJ R\\V]YRY',
	    'PoUN\\N R`QfQ RUR\\R RfUjU R`[k[ RUJUY RYGYJ RUJ\\J\\R RaQa[ R`KfKfQ RaHkHkT RYC]H RlXl[ RYCRJ RbC^L R[U\\YRY',
	    'Po`HlH RUN\\N RUR\\R RUJUY RYGYJ RUJ\\J\\R R`L`W RfGf[ RXC]H RWERJ RbC^L R[U\\YRY R`LkLkWiW',
	    'Po`NkN RTO\\O RTS\\S R_ZlZ RTKTZ RXHYK RTK\\K\\S R_Q_Z R`F`N RfCfZ RkFkN RlQl[ RXD]H RXCRI R[U\\XSZ',
	    'Po]GlG RTO[O RTS[S RTKTZ RXHXK RTK\\K\\S RaCaJ RgCgJ RXD\\H RXDRI R`LcTiYl[ RZV[XS[ RiLfT`Y\\[',
	    'PoTOZO R`OlO RTSZS RaZlZ RTKTZ RWHXK RTKZKZS R_I_[ RgFgZ RWD[H RWDRI RaC]M RYUZYRZ RkDaG',
	    'PoVH[H R_KlK RUN\\N RUR\\R RUU]U RUX]X R`ZkZ RUKU[ RUK\\K\\R R`R`[ RfEfR R`RkRk[ RXC]H RXCRJ RjD_E',
	    'Po^HlH RVH[H RTN\\N RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q ReCeH RXC]H RhJlN RWDRJ RbK^O RaOdUiYl[ RhOeUaY][',
	    'Po`LhL RTO[O RTS[S R_ZiZ RTKTZ RWHXK RTK[K[S R_Q_[ R_QiQj[ RWD[H ReFlM RdCfH RWDRI RbG]M RcCaI RYU[XRZ',
	    'PoVH[H R_IlI RTN\\N R^PlP RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\R RiIi[ RXC]H RXDRJ R`CbH RjChH RbIbSaX',
	    'PoTO[O RTS[S Rg[l[ RTKTZ RWHXK RTK[K[S RgDg[ RlWl[ RXD\\H RfPkT RaW][ RXDRI R_HaM RjGhM RbP_S^S RYU[YRZ RcDcQaW',
	    'PoVH[H RbIjI RTN\\N RTR\\R RcThT RTU\\U RTX\\X Rh[l[ RTKT[ RTK\\K\\R R_E_[ RcMcV RcMhMhT R_ElEl[ RYC]H RXCRI',
	    'PoSEZE RXTfT RXWfW RXQXZ RXQfQfV R^FkFhH R_D[H R_LiQ RcGfJjL R_LTQ RiWbY R\\ObOlQ RZJRK RbG_JYL R]WcZk[ R`ZZZU[ R]ZW[',
	    'PoVH[H RUN\\N RUR\\R RcUhU RUU]U RUY]Y Rh[l[ RUKU[ RUK\\K\\R R_H_[ RcMcV RcMhMhU R_HlHl[ RYC]H RXCRJ RfCdH',
	    'PoTFjF RVJhJ RRMlM RXSfS RXVfV RV[^Z RYOYZ R_F_L RWPfPfV RYC\\F ReDcF RcMfPlR R[MXPRR R_WcYj[ RiWcX',
	    'PoUGjG RRJlJ RXSfS RXVfV RXPX[ RUQfQfV R^NaP RWDYF RfDdF RbJkN R_LjQ R_C]GZKRN R_LTQ RhWdY R`XjZ R_YWZ',
	    'PoVH[H RaKjK RUN\\N RaQjQ RUQ\\Q RUU]U RUX]X RUKU[ RUK\\K\\Q RaEaW R^EkEj[ RXC]H RXCRJ RkV^W',
	    'PoTDlD RWIkI R[ThT R[WhW R[Q[[ RaOcQ RbJjM RbMlR ReGhH RaG]KWM RaL^OVR RYQiQiWeY R`IgK R`Xj[ RaZY[ RTDTTSZ',
	    'PoTO[O RTS[S R`ZjZ RTKTZ RTK[K[S R`Q`[ R`RjRj[ RXD\\H RcP^R RhJaQ RXCRI RWHXK RbGdLhPlR RcC_J RbGiFfN RZU[YSZ',
	    'Po^HlH RTO[O RTS[S RTKTZ RWHYK RTK[K[S RbMbZ RhUlY RXD\\H RbM]R RkLfQ RWDRJ ReNjX RdCdF RdHaO RfY`[ RdIgT RZU[YSZ',
	    'PoZE_E RXTgT RXWgW RXQX[ RZCZG RVHRJ R_LjQ RUIZK RaHjK RVH^HZLSO R^LSQ RaEjEfJaL R\\OcOlQ RXQgQhWbYk[ R`Z[ZU[ R^ZW[',
	    'Po_FlF RVH[H RUN\\N RUR\\R R_TlT RUU]U RUX]X RUKU[ RUK\\K\\Q R_K_N ReCeK ReRe[ R_KlKlN RXC]H RaOiOeS RXDRJ Rb[e[b[',
	    'PoVH[H RTN\\N R^QlQ RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q RdGeK RXC]H RWDRJ RiFlL R`Uk[ RcM_V R`E^L RiQgVeXbZ][ RiC`E',
	    'Po]LkL RUN[N RUR[R RUJUY RXGXJ RUJ[J[R RXC\\H RXCRJ RiDjH RkOf[ RdD]E RZT\\YSY RaEa[^[ RdR]S RgDgPiZk[mW',
	    'Po^GlG RVH[H RTN\\N R_PkP RTR\\R R_UkU RTU\\U RTY]Y Rh[k[ RTKT[ RTK\\K\\R R_K_[ ReCe[ R_KkKk[ RXC]H RhDkE RXDRJ',
	    'PoVH[H R_IkI RTN\\N RTR\\R RaRiR RTU\\U RTX\\X RTKT[ RTK\\K\\Q RgIgNlNlO RXC]H RhFlJ RbQ]U RWERI RaSfYl[ RcC`I RbI`N^O RiSeX][',
	    'Po_ElE RVH\\H RUN]N R`QjQ RUR]R RUU]U RUX]X R^ZlZ RUKU[ RUK]K]R R`I`P R`IjIkP RYC]H RXCRJ RbSbY RhSgY',
	    'Po^GkG RVH[H RTN[N R_QkQ RTR[R RTU\\U RTX\\X RTKT[ RTK\\K\\Q R_K_Q ReCe[ R_KkKkQ RfSlY RXC\\H RcS]Y RWERI',
	    'PoVH[H R`LiL RTN\\N R^QkQ RTR\\R RTU\\U RTY\\Y Ra[e[ RTKT[ RTK\\K\\R ReLe[ RXC]H ReElK RWDRJ RcF]K RiTkY RdCbH R`T^Y',
	    'Po^LlL RTO[O RTS[S RhYkY RTKTZ RWHXK RTK[K[S R`P`Y ReNe[ R_RkQkX RXD\\H RaO]S RXDRI RcI_R RjD^I R^DlH RZU[YRZ',
	    'PoVH[H R^LlL RUN\\N RUR\\R RbSjS RUU\\U RbWjW RUY]Y Rh[j[ RUKU[ RUK\\K\\R RbNb[ RaPjPj[ RXC]H RbN^S RXDRJ R`DkI RdJ`Q RiE_I',
	    'PoVH[H R_IkI R_MkM RTN\\N RTR\\R R`SdS RTU\\U RTY]Y Rf[l[ RTKT[ RTK\\K\\R R_D_M R`O`Z RgOg[ R_DkDkM RlXl[ RXC]H RWDRJ RkRfT RdY]Z',
	    'PoVH[H R_IkI R_NkN RTN\\N R^QkQ RTR\\R RTU\\U RTY\\Y RTKT[ RTK\\K\\R R_D_N ReDe[ R_DkDkN RgTlY RePgT RXC]H RbT^Y RWDRJ RePcSaV',
	    'Po^FlF RVH[H R^MlM RTN\\N RTR\\R RTU\\U RTX\\X Re[i[ RTKT[ RTK\\K\\Q RbCbI RhChI RXC]H RaW][ RXDRJ ReJeN RbSjSi[ RcMaW',
	    'PobGiG R_GfG RVH[H RTN\\N RTR\\R RbRiR R]SeS RTU\\U RTX\\X RTKT[ RTK\\K\\Q RkKkN RXC]H RlXk[ RWDRI RaQeX RaDdJfLjM ReOhO RiTdWgZj[ ReDhD RgW^[ RiIcL^M',
	    'PoUHZH RTN[N RTR[R RTU[U RTX[X RTKT[ RTK[K[R RgVl[ R`UcX RXC\\G RgHkM RjHeM RaV\\[ RjUdZ RWERI RaJ]N ReQiY R^HcJ R]TaV ReEkEhK RdPkPhX R]EcE`L R]PcQ_X',
	    'PoVH[H R^IdI ReIlI RTN\\N ReQlQ RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q RhIh[ RXC]H RXDRJ RiCgI RdP]R RbD`IaS`Y',
	    'PoVH\\H RfLkL RUN]N RUR]R R`TdT RgTlT RUU]U RUX]X R`ZlZ RUKU[ RUK]K]R R`P`[ RgPlPl[ RYC]H RYCRJ RcD_M RdN`P RcGlFkL',
	    'Po_GkG RVH[H RUN\\N R^NlN RUR\\R RUU]U RUX]X R`ZjZ RUKU[ RUK\\K\\Q R`R`[ ReCeG R`RjRj[ RXC]H RXCRJ RiHhM RaHbM',
	    'Po]GkG RTO[O R_RjR RTS[S R_VjV Rd[k[ RTKTZ RWHXK RTK[K[S R_M_X ReKe[ R_NjNjV RkXk[ RWD[H RgIlN RXDRI RbI]N RdC`L RYUZYRZ',
	    'PoVH[H RTN\\N RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q RfVj[ RXC]H RhKkN RjEiI RdV_Z RkQiU RWDRI ReSgY RdIhK ReSbX R`F_I R`Q_U RdJaM^N ReCcK ReOfV',
	    'PoaEjE RTH]H RaHjH RSK]K RaKkK RXSfS RXVfV RXPX[ RaCaK RXPfPfU R^N`P RiVdX R_KdNlP R_WkZ RTE]D^LXORP R_YV[',
	    'Po`DjD R`HiH RVH[H R`LiL RTN\\N R^PlP RTR\\R RTU\\U RTY]Y RTKT[ RTK\\K\\R R`D`Z RfUk[ RXC]H RjRgV RWDRJ RdPgX RfY]Z',
	    'PoVH[H R^IlI RTN\\N RTR\\R R]RlR RTU\\U RTX\\X RTKT[ RTK\\K\\Q ReEeO RXC]H ReHjM RWERI RdH^N RcP`WkZ RiRgVeY][ RiD_E',
	    'PoUN\\N RaPjP RUR\\R RaZkZ RUJUY RYGYJ RUJ\\J\\R R_F_J RaKa[ ReCeF RaKjKjP RaTkTk[ R_FlFlJ RYC]H RWERJ R[U\\YRY',
	    'Po_KjK RTO[O R_PfP RTS[S RTKT[ RXHXK RTK[K[S RgVkZ RXD\\H RdHeK RcL_P RWDRI ReC^J ReDiHlI RhNcS^U RZU[YSZ RjRdX_[',
	    'Po^ElE RVH[H RTN\\N RTR\\R R^TlT RTU\\U RTX\\X RTKT[ RTK\\K\\Q R_H_P RdHdP RgHgP RfVjZ RXC]H RWDRI ReSgY RdEcK RdVaY][ ReQcX R_HkHkPiP',
	    'PoVH[H R_IkI RTN\\N R_PkP RTR\\R R_TkT RTU\\U R^WlW RTX\\X RTKT[ RTK\\K\\Q R^E^H R_L_T ReGe[ R_LkLkT R^DlElH RXC]H RWDRI',
	    'PoUHZH R\\IdI RfKlK RTN[N RfRlR RTR[R RTU[U R^WcW RTX[X Rh[l[ RTKT[ RTK[K[R R^O^Y R`C`O R^OcOcW RfDlDl[ RXC\\G RWERI RfDfReX',
	    'Po_JkJ R_OkO RTO\\O R_RkR RTS\\S R_VjV R^[l[ RTKT[ RWHYK RTK\\K\\S R_F_N ReRe[ R_FkFkN RXD]I RXDRJ RZU\\Z RdCdG RZWS[',
	    'PoSE_E RXTfT RXWfW RXQX[ R_LjQ RXETIYK RfDlG RVH]HYLRN RfG`H R^LSQ RXQgQhWbY RdE^G R\\OcOlQ RgIbK RiLcN R]WcZk[ R[ZU[ R_ZYZ',
	    'Po_JhJ R]LlL RTO[O RTS[S RTKTZ RWHXK RTK[K[S RWD[H RXDRI RhRkY RdLbO^Q RcR^U RYU[YRZ ReTbW]Z RaC_I RfVe[a[ RkOeRfX RaFiEhL',
	    'PogElE RUHZH RTN[N RTR[R RTU[U RTX[X Rg[j[ RTKT[ RTK[K[R R`K`[ RgLkLj[ RWC[H RbKeL RcDeF R`E\\H RVCTI R`H]Q ReScZ',
	    'PoUHZH RaJlJ RbOkO RTN[N RTR[R R`TlT RTU[U RTX[X RTKT[ RTK[K[R R_G_[ RcDiEiJ RhWk[ RXC\\G ReWa[ RWERI RgNgRiY R`D\\M RcKaQ RfPeW',
	    'PoVH[H R`IkI R`MkM RUN\\N RUR\\R RUU\\U R_XiX RUY]Y Rg[k[ RUKU[ RUK\\K\\R R_R_Z R`E`L R`EkEkL R`QlPlY RdShW RXC]H RbN]S RXDRJ ReQcV',
	    'Po`HjH RVH[H R`LjL RTN\\N R^OlO RTR\\R RTU\\U RTX\\X Rg[j[ RTKT[ RTK\\K\\Q R`D`L R`DjDjL RXC]H RaQ]U RcT^Z RWDRI RhRdZ R_SlSj[',
	    'Po^GlG RVH[H R`MiM RTN\\N RTR\\R R]TlT RTU\\U RTX\\X RTKT[ RTK\\K\\Q RdJdQ RhPh[ RXC]H RgHlM RWDRI RbH^M RdCaK RaR`Y',
	    'PoVH[H R_IkI R_MkM RTN\\N R]QlQ RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q R_E_M R`Q`Z RdEdM R_EkEkM RfVk[ RXC]H RjTfV RWDRI RdQgX RfY]Z',
	    'PoTO[O R`SjS RTS[S R`VjV Rf[j[ RTKTZ RWHXK RTK[K[S R]L]O R`O`[ R`D`L R`DjDjL R`OjOj[ R]LlLlO RXD\\H RXCRI R`HeHeL RZU[YRZ',
	    'Po^GlG RTO[O R_SiS RTS[S R_WiW R][l[ RTKT[ RXHXL RTK[K[S R_O_W RdDdM R_OiOiW RWD\\H RXDRI RdGhKlL RdG`K]L RZU[YSZ',
	    'PobIjI RbOkO RTOZO RTSZS RbZlZ RTLT[ RWHWL RTLZLZS R\\N_N_WbZ RWD[H R_W\\Z RWDRI R]E_I RfIfPhRjW RcDdG RiDfK RYUZYS[ R_N\\N RfLeT',
	    'Po^FlF RVHZH R^MlM RTN[N R`QjQ RTR[R RTU[U RTX\\X R`ZjZ RTKT[ RTK[K[R R`T`[ ReCeF ReHeS R`JjJjQ R`TjTj[ RXC\\H RWERI R^F^S\\Z',
	    'Po]FlF RVH[H R_LkL RTN[N R_PkP RTR[R R]TlT RTU[U RTX\\X Rd[h[ RTKT[ RTK[K[R R_I_R ReCeQ RhRh[ R_IkIkQ R`VcX RXC\\H RWDRI RgDjD',
	    'Po_HkH RVH[H R_MjM RTN\\N RTR\\R R^SlS RTU\\U RTX\\X RTKT[ RTK\\K\\Q RbVc[ ReHeS RXC]H RWDRJ RfVgZ RaDaG RiUl[ RhCgG R_V][',
	    'Po_GkG RVHZH R`JjJ RTN[N RTR[R R]TgT RTU[U RTX[X RTKT[ RTK[K[R RbOb[ RlXl[ RdVgY RbTeW RXC\\H RcT]Z RVERI R_O_S RfOeR RaC]J R]MiMiVj[',
	    'PoVH[H R_KkK RTN\\N R_PkP RTR\\R RTU\\U RfWlW RTX\\X Re[l[ RTKT[ RTK\\K\\Q R_F_P ReFeZ RhRgW R_FkFkP RXC]H R`W\\[ RWDRI RdCcF RjTlW RaP`X',
	    'PoVH[H RTN[N RhNkN RTR[R RTU\\U R_UkU RTX\\X R_ZkZ RTKT[ RTK[K[R R^E^M R_Q_[ ReQeZ R_QkQk[ ReElElN RXC\\H RWDRI ReNcN RcD^E RcIdL]M RgEfM',
	    'Po_JbJ RgJkJ R_NkN RTO[O R^RjR RTS[S RTKTZ RWHXK RTK[K[S R_F_N ReCeR RgFkFkN RWD[H RXCRI R`SeXk[ RYU[YRZ RjSeX^[ RbE_F',
	    'PoVH[H RTN\\N RTR\\R R_TdT RfTkT RTU\\U RTY\\Y R_ZkZ RTKT[ RTK\\K\\R R_P_[ ReGeK RfOkOk[ RXC]H RWDRJ RiFlL RdN_O R`E^L RjD`E',
	    'Po_FjF RVH[H R^KlK RTN\\N R`PjP R^PhP RTR\\R RTU\\U RTX\\X R\\ZlZ RTKT[ RTK\\K\\Q R_S_Z RbSbZ RdCdJ RfSfZ R_SjSjZ RXC]H RhMlQ RWDRI RbK`P',
	    'PoVH[H R_MjM RTN\\N RTR\\R RTU\\U RTX\\X R\\[l[ RTKT[ RTK\\K\\Q R^Q^Z R_D_M RcQcZ RfQfZ R_DjDjM R^QjQjZ RXC]H RdHhK RWDRI RdFbK',
	    'Po]FkF R^MjM RTO[O R^QjQ RTS[S R]UkU RTKTZ RWHXK RTK[K[S R^J^Q RaCaH RgCgH R^JjJjQ RWD[H RXDRI ReVhYl[ RYUZYRZ RcV`Y\\[ RdQbX',
	    'PoTO[O R`RiR RTS[S R_VjV R][l[ RTLT[ RWHXK RTL[L[S R^I^L R`L`R RdCdH RdRd[ R`LiLiR R^IkIkL RXD\\H R_D`G RXDRI RjDhH RZU[YR[',
	    'Po_GjG RVH[H R_JjJ RTN\\N R^QkQ RTR\\R RTU\\U RTX\\X RTKT[ RTK\\K\\Q R^M^Q R_D_J RbMbQ RgMgQ R_DjDjJ R^MkMkQ RXC]H RWDRI R`TcXgZl[ R^TjTgXcY][',
	    'Po^HkH RVH[H RTN[N R]PlP RTR[R R]SlS RTU[U R^WkW RTX\\X RTKT[ RTK[K[R R^D_L`S RdDd[ R^LjLiS R^DkDkL RXC\\H RWDRI',
	    'Po^ElE RVHZH RbMiM RTN[N RTR[R RTU[U RgVkV RTX\\X R^YlY RTKT[ RTK[K[R R^E^[ RaPaV RbHbL RgPgV RbHiHiL RgPkPkV RXC\\H RWERI RaPdPdV`V',
	    'Po^GkG RVH[H R_KkK RTN\\N R]NlN RTR\\R RTU\\U R`UlU RTX\\X R_ZlZ RTKT[ RTK\\K\\Q ReGeN RbQiQiY RXC]H RWDRI R`CbG RhCgG R`P]Z RdQcZ',
	    'Po^FlF RVH[H RaIiI RTN\\N R_QjQ RTR\\R R^TkT RTU\\U R_WjW RTX\\X R][l[ RTKT[ RTK\\K\\Q R_L_Q RaCaI ReIeZ RiCiI R_LjLjQ RXC]H RWDRI',
	    'Po[GcG R[KcK RTOYO R\\RbR RTSYS R\\VbV R`[b[ RTKTZ RWHWK RTKYKYS R\\N\\[ R]C]K RaCaK R\\NbNb[ RVDZH RXUYZ RgRkZ RiReZ RVCTI RXWRZ RfGfNiX RjMgX RfJhW RfIkIhV RfDdN',
	    'PoVH[H R_IjI R]KlK RTN[N R_QjQ RTQ[Q R_SjS RTU\\U R_VjV RTX\\X RTKT[ RTK\\K\\Q R_E_H R_N_V RdCdK R_NjNjV R_EjEjH RXC\\H RWDRI RgYl[ RbX\\[',
	    'Po^HcH RfHkH RVHZH RTN[N R]QkQ RTR[R RTU[U R]UlU RTX\\X RTKT[ RTK[K[R RaNaU R^DcDcH RgNgU RfDkDkH RgXkY RXC\\H RWERI RaX\\[ RfDfLkKkL R^D^LcKcL',
	    'Po_GkG RVH[H R_JjJ R^MlM RTN\\N RTR\\R R]RlR RTU\\U RTX\\X R_ZjZ RTKT[ RTK\\K\\Q R_U_[ ReGeR R_UjUk[ RXC]H RaDbG RWDRI R`NaR RiNhR RhCgG',
	    'Po^FlF RVH[H R^IkI RTN\\N R_OjO RTR\\R R]SlS RTU\\U RTX\\X R_ZjZ RTKT[ RTK\\K\\Q R_V_[ RaPbS RdCdI R_VjVj[ R_KjKjO RXC]H RWDRI RgOgS R_K_O',
	    'PoUI]I RXUfU RXWfW RXSX[ RXSfSfW R^P`R RTD]D[G RbIfKjM RcCaI RbFjFgJeLaN RiWdY R`Yj[ RRG^G^NTR R`PlR R[MTN RUGUM]LWN R_ZW[',
	    'Po^FlF RVH[H R_IkI RTN\\N R`OjO RTR\\R RTU\\U R`UjU RTX\\X R][l[ RTKT[ RTK\\K\\R ReCeI R`QjQjU R^L^MlLlN RXC]H RWDRI RaWbY RhWhZ R`Q`U',
	    'PoVHZH RTN[N R^QlQ RTR[R RTU[U RTX[X RTKT[ RTK[K[R RgXj[ R_TbW RXC\\G R]H`J RfGhJ RlXk[ RWERI RgOjP RjGgM R`D^H RaG^N RhDgH RjTfX`Z RbKbN]N RkKkNeM RdCdKfX R^R]Y',
	    'Po^FkF RVHZH R]JlJ R]McM ReMkM RTN[N RdQlQ RTR[R R]TlT RTU[U RTX\\X RTKT[ RTK[K[R R`K`Q RdCdJ RhKhQ RgTg[lZl[ RXC\\H RWERI RbT`Y\\[ RcP]Q',
	    'PoTO[O R`PiP RTS[S R]TlT RTKTZ RWHXK RTK[K[S R]H]K R`K`P RdCdH RdPd[ R`KiKiP R]HkHkK RdSkZ RWD\\H R_D`G RiDhH RdS^Y RXDRI RYU[YSZ',
	    'PoREkE R_HjH R_LjL R^NkN RXUfU RXWfW RU[^[ RXSX[ ReFeN RUHWJ RXFUH RZGUL R`G_NcP RSL[LYNUP RdJhJ RaJeJ RZQaPlS R_NYQRS RXSgShWbY R`E_JcJ R`Yk[ R`Z\\Z',
	    'PodDjD RTG[G RRI]I RaIfJkJ RfOkO RXUfU RXWfW RX[_[ RU[\\[ RTDTG RXSX[ RTD[D[G ReCeJ RbL`NgR R_FkFjH RfLfO RZQbPlR RUITL[KZOWO R_NXQRS RXSgShWbY R`Yk[ R_F^M',
	    'PoeFjF R\\IlI R]MlM RTOZO R_PeP RTSZS RTKTZ RWHXK RTKZK[S R`E`I ReCeI RlXl[ RWD[H RbPcW_[ RhVc[ RWDRI RhKjK RdSbX RjPgX R`S^W RYUZXRZ RfLhY R]M]S\\Z',
	    'Po[LcL RXTfT RXWfW RXRXZ R[E[M ReDeN RXRfRfW RSFVI RiHlK ReEkDiH RVDTG RYFSL RRLYKUO R_MgQ R^WeZ RiWbY R_FcG R\\PaOlR R[H_H[I R[EbEbH_H R_MYPRR RkMhM RU[[ZaZl[ R_F[G',
	    'Po]FlF RVH[H RbMgM RTN\\N R_PjP RTR\\R RTU\\U R_UjU R_XjX RTX\\X R\\[l[ RTKT[ RTK\\K\\R R_H_O R_R_X RbJbM RdCdF RbJgJgM R_RjRjX R_HjHjO RXC]H RWDRI',
	    'Po_FkF RVH[H R^JlJ RTN\\N R_PjP RTR\\R R_TjT RTU\\U R_WjW RTY\\Y RTKT[ RTK\\K\\R R_M_W RaHaL ReCeH RhHhL R_MjMjW RXC]H RWDRJ RgXl[ RbX][',
	    'Po]FlF R^JjJ R]MlM RTOZO RbRjR R^RfR RTSZS R][l[ RTLT[ RWHWL RTL[L[S R^U^[ RaCaH RbUb[ RfUf[ RhChH R^UjUj[ RWD[H RgOkS RdHdM^R RVERJ RZXSZ RYUZZ',
	    'Po]FlF RUHZH R`MiM RTN[N R]PlP RTR[R RTU[U RTX[X RTKT[ RTK[K[R RbCbH RgCgH R^J]KkJkL RgUkY RXC\\G RWERI RjRgU RePhW ReP]S RcT\\W RdW\\Z Rd[a[ RdTeY',
	    'PoTDkD RaIkI RgMkP RhGjG RfHiO RdOaO R[S`QkT RXTXZbXg[ RWKWOZNWN`L`P[RRU R_N\\N`N RfFeN RWF_F_I[I RfVcV RXVZUgThXdY RT[`Zk[ RTDTKSR RWFWI[HWH R^VaV_V',
	    'Po]FlF RfIlI RTOZO R_QlQ RTSZS RaZjZ RTKTZ RWHXK RTKZK[S RbSb[ R_IcIbO RhHhO R`UjUj[ RWD[H RaIdL RhIlN RbS^W RWDRI RbH_N RhHfM RYUZXRZ R]F]T[[',
	    'PoUHZH R]JjJ R^LcL RTN[N ReOlO R_QfQ RTR[R RTU[U R]WkW RTX[X RTKT[ RTK[K[Q RfJfO R]HjGjJ R\\TjSjW RdWd[lZl[ RgXjY RWC[G RWDRI R_EfEdG ReRbX`Z[[ R`P]S]V R`C]H^N RjLeL RcO\\O',
	    'Po]ElE RVHZH RfKkK R]NkN RTN[N R^QkQ RTR[R R]TlT RTU[U RTX\\X RTKT[ RTK[K[R RaVaZ R^HcHcL RdCdE RgKgS RfHkHkL RXC\\H RWERI ReTfWiZl[ R^H^KbKbT]W RkUgW RfHfL RfY]Z',
	    'PoUHZH R]IkI RTN[N RdOlO RTR[R RdRlR R\\TcT RTU[U RdVlV RTX[X RdZlZ RTKT[ RTK[K[R R]D]I R_T_[ RbDbI ReMe[ RfDfI RiMiZ R]DkDkI RaQbT R\\N_Q RWC[G RVERI RbVcY RiKhP RaM^R R]V\\Z ReKcQ R^K^O',
	    'Po\\FbF ReFkF RdIlI RiMlM RTOZO R^QjQ RTSZS R^TjT R^WjW RTKTZ RVHWK RTKZKZS R^O^W R^OjOjW RVDZH RfXl[ RVCTH R]D\\G R_C^I]M ReDeG RhCgIeM R\\IbIaM_M RYUZXRZ RaX[[ RhFiL',
	    'Po\\DkD R^IiI R]NbN ReNjN RTOYO R]QkQ R^SjS RTSYS R\\UlU RTKTZ RWHWK RTKYKYS R`W_[ RaOaU R]L]MbLbN RdCdJ RgOgU R^F^HiFiI ReLeMjLjN R[J[LlJlM RVDZH RdUgXkZ RVCTI RXUYYRZ RdU\\Y ReZ_Z',
	    'Po[M[Z RXCSN RVHcH`M RbWY[',
	    'Po_FlF Rb[g[ RWMWZ RgFg[ RUH]H[M R\\XVZ RVDSN',
	    'Poh[l[ RWLWZ R`EhEh[ RlVl[ RUH\\HZL R`E_T\\[ R[WVY RVDSN R_N^X',
	    'Poc[k[ RWMWZ RdFd[ RlWk[ RUH[HZM R[XUZ RVDSN RjD]G RgO\\P RiN^P RkNbO',
	    'Pof[j[ RVMVZ RaP\\U ReT][ RhLcV RUI[HYM RbL_S R[WUZ RUDSN R\\EiEfH]LlLlRjZ',
	    'Po]GlG R^SjS Re[l[ RWMWZ R^K^S ReCe[ RjKjV RlXl[ RUH[HYM R[XUZ RVDSM',
	    'Po`GkG R`JiJ R]OgO RVMVZ RlWl[ RUH[HYM RaC]K R[XUZ RVDSN RgPiZ',
	    'Po]OlO R][l[ RWLWZ ReFe[ RUH\\H[M R\\XVZ RVDSN RjE^F',
	    'Po^NlN RWMWZ RgVl[ RbU][ ReEeQiY RdQaX RUH\\HZM R\\WUZ RdMgW RUDRN ReKfU RjD^F RdKcU',
	    'Po`HlH Re[i[ RWMWZ RaW][ RUH[HYM RaC]L RbR_Y R[WUZ RVDSN R^OkOkTi[ RcJaW',
	    'PoVMVZ RdTjZ RaMeV RUH[HYM RZWVY R^LjLgUbY^[ RVCSN R^F^R\\Z RjD^F',
	    'PoWLWY R`HkHjL RgVl[ RbU\\[ ReQhY RUH\\H[M RdQaX R[WUZ RdMgW RVCSM RaC_K RdKePcU',
	    'PoWMWZ RlXl[ RgZk[ RfDjF RUH\\HZM R[XVZ RkQfV]Z RVDSM RcCcLeW RfI^J RiIaJ RkM]P',
	    'Po_HlH RhWkW RVMVZ R^N^W ReGe[ R^NkNkW RUH[HZM RaC]K R[WVY RVDSN',
	    'Po_SfS RfUjU R_[k[ RVMV[ R`K`[ R_KfKfR R_GjGjT RlXk[ RZWV[ RUI[HYM R`C]L RVDSN',
	    'Po^IiI R_UgU Rg[l[ RWMWZ R_N_W R_NgNgU R]ElEl[ RUH\\HZM R[XUZ RVDSM',
	    'Po_MlM R^ZlZ RWMWZ R^Q^Z R_F_M ReDeZ RlFlM RlQl[ RUH[HZM R[XUZ RVDSN',
	    'Po^ZiZ RWMWZ R^Q^[ R^QiQi[ R\\MdMkLlN RgHjK R`I\\M RUH[HZL RbC`I R[XUZ RVCSM',
	    'Po`KjK R`QjQ RWLWZ R`E`W R^EkEj[ RUH]H[M R\\WVZ RVCSM RiV^X RlVdW',
	    'PoeGkG RaGgG R^GdG R\\RkR Rf[l[ RVMVZ RfRf[ RkLkO RlXl[ R`W\\[ RaFcKfNiO RTHZHXM RYWUY R`CbK RUDRN RaR_Y RiJcM]N',
	    'PobUhU Rh[l[ RWMWZ R^H^[ RbMbV RbMhMhT R^HlHl[ RUH\\HYM ReCdH R[WUZ RVCSM',
	    'Po`LhL R_ZiZ RVMVZ R_Q_[ R_QiQj[ ReFlM RbG\\M RdCfH RUH[HYM RcCaI RZWUY RVDSN',
	    'PoaGjG R_ZjZ RVMVZ R_Q_[ R_RjRj[ RbF]K RaIgPlQ RUH[HZM RcCaH RiHcO]R R[XUZ RVDSN',
	    'Po]GlG RVLVZ RgJlO RcCdF RaJ\\O R`ObTgYl[ RUH[HYM RZWVY RhOeTaX[[ RUDSN',
	    'Po]IkI RVMVZ RaNaZ RhUlY RbM\\R RkMfR ReNiW RdDdG RUH[HYM RcIbM RZWUY RdX_[ RdJgT RUDSN',
	    'Po]IkI R\\QlQ RVMVZ RgHg[ R`DaH R`W\\[ RUH[HYM RiCfI R[WVY RUDRN RaIaR`W',
	    'Po]FlF R_OjO R\\TlT Ra[d[ RVMVZ R]K]N RdCdK ReRe[ R]KlKlN RYWVZ RUH[HYM RiPdR RUDSN',
	    'Po^ElE R_QjQ R\\[l[ RVMVZ R_J_Q R_JjJjQ RiSf[ RUH[HZM R[XUZ RaSbY RVDSN',
	    'Po\\LlL R[[_[ RVNVZ R`E`[ RhDlH RhTbZ RkNgV RUH[HYM R[XVZ RVDSN RcD\\F RcQ[S RfCfOgY RlWk[i[',
	    'Po_KjK R]QlQ R`[d[ RVMV[ RdKd[ RdDhHlJ RiTkY RUH[HYM R_T]Y ReC`H\\J RZXVZ RUDRN',
	    'Po\\FbF R\\PlP RWMWZ R]H_L RcGdK R_UkZ RiGgL RUH[HYM RcL_T R[XUZ RVDSM RhPfUcX`Z[[ RjD`E',
	    'Po`IjI R]IhI RVMVZ RgKkO RfEkJ RbO[U RcC]I RaK\\O R_RcXk[ RUH[HYM R[WUY RVDSN R_RiRdW`YZ[',
	    'Po^IjI R^NjN R\\RlR RVMVZ R^D^N RdDd[ R^DjDjN ReSlY RcS\\Z RUH[HYM R[WUZ RVDSN',
	    'Po]IjI R]MjM R^ScS Re[l[ RVNVZ R]D]M R^P^[ RfOf[ R]DjDjM RlXl[ RUH[HYM R[XVZ RkReT RVDSN RcZ]Z',
	    'Po^TdT RgTlT R^ZlZ RVMVZ R_O_[ RfOlOl[ RaI]M RbGjGhL RUH[HYM RcC_K RdN^O R[WUZ RVDSN',
	    'Po`PiP R`ZkZ RVMVZ R]F]J R`J`[ R`JiJiP R`TkTk[ R]FlFlJ RdCeF RUH[HYL R[WVY RUDRN',
	    'Po]GkG R_SjS R_WjW R\\[l[ RVMVZ R_O_W RdCdN R_OjOjW RdGkM RYWU[ RdG]N RUH[HYM RUDRN',
	    'Po]IjI R\\LlL RVMVZ R]E]I R]O]W RcCcL R]OiOiW R]EjEjI ReXk[ RUH[HYM RZWVY RbX\\[ RUDSN RcRcX',
	    'Po_RiR R_ViV Rf[i[ RWMWZ R]K]O R_O_[ R_OiOi[ R_DjDjK R_D^KlKlO RUH[HYM R[WUZ R_HdHeL RVDSN',
	    'Po]JaJ RfJkJ R]NkN RVMVZ R^F^N RdCdR RfFkFkN RUH[HYM R[XUZ RbE]F R]RbWfYl[ RVDSM R\\SjSgWbYZ[',
	    'Po^MkM ReRlR RVMVZ R^H^M RfMfR R]IkIkM RaD\\I RbThV RUHZHXM R`FgEeHbO`Q[S RZXUZ R^Xi[ RUDRN',
	    'Po^GjG R\\KlK R]QjQ R[[l[ RVMVZ R^S^[ RbSb[ RfSf[ R^SjSj[ RgMkQ RdCcK]Q RUH[HYM RZXVZ RVDSN',
	    'Po]FlF R^MjM R^QjQ R]UlU RVMVZ R^J^Q RaCaH RgCgH R^JjJjQ RYWV[ ReVhYl[ RUH[HYM RcV`Y\\[ RUDSN RdQbX',
	    'Po]FlF R^MkM R^PkP R]TlT Rd[h[ RWMWZ R^I^R RdCdQ RhRh[ R^IkIkQ RaVcX RgDjD RUH[HYM R[WVZ RVDSN',
	    'PohNkN R^UkU R^ZkZ RVMVZ R]E]N R^P^[ RdPdZ R^PkPk[ ReElElM RUH[HYM RbHcK]M RZWUY RVDSN RcD]E RgEfM',
	    'Po]GkG R^KkK R\\OlO R_ViV R\\[l[ RVMVZ R_RiRiZ R_D`F R`Q[V RUH[HZM RcJ^S RhCfG R[XUZ RVDSN RcGaQ RcRb[',
	    'Po]FlF R`IhI R^PkP R^SkS R^WkW R\\[l[ RWLWZ R^L^P R`C`I RdId[ RhChI R^LkLkP RUH\\HZM R[WVZ RVDSM',
	    'Po_GjG R_JjJ R]QkQ RVMVZ R_D_J R_DjDjJ RUH[HYM RZWVY RbMbQ RfMfQ R_SbXgZl[ R]MkMkQ RUDRN R]M]Q R]TjTgWcY[[',
	    'Po[GcG R[KcK R\\RbR R\\VbV R`[b[ RUMUZ R\\N\\[ R]C]K RaCaK R\\NbNb[ RgRkZ RiReZ RTHZHXM RYXUY RfMiX RjLgX RUDRN RfJhW RfIkIhV RgCdO',
	    'Po]HcH ReHkH R]LcL RfLlL R\\QlQ R\\VlV RVMVZ R^D^L RaNaU R]DcDcH RfDfL RgNgU ReDkDkH RgXkZ RUH[HYM R[WUZ R`X[[ RVCSM',
	    'Po\\EkE R^IiI R]NbN ReNjN R\\QkQ R]SiS R[UlU RVMVZ R_W^[ R`P`U R]L]MbLbN RcCcJ RgPgU R^F^GiFiH ReLeMjLjN R[J[LlJlM RiWfX RdUgYl[ RTHZHXM RYXUZ RUDRN RdZ^Z RcU\\YZY',
	    'PoRGlG RVPhP RVUhU RVYhY RVKV[ R_G^K RVKhKh[ RXCZF RfCcG',
	    'Po\\GlG R_OiO R_SiS R_WiW R\\ZkZ RRI[I\\Z R_K_V RcGcK R_KiKiV R`CaF RhDeG RlVkZ RVCVPUX',
	    'Po^HlH RRH\\H RSP[P R^RcR RSU[U RSY[Y Ri[kZ RSLS[ RSL[L[Z R^L^R R^LcLcR RhDkF RhTbZ RlWk[ RTDUG RkMgV RWHUL RZCXH RdU]V RgCgPhZ',
	    'PoRJlJ RWVhV RWZhZ RWQW[ RUFZF_E_P RWQhQh[ RaJdNlQ R]JYNRQ RgD]E',
	    'PoRH]H RTU\\U RTY\\Y Rd[h[ RTQT[ RYDYO RTQ\\Q\\Z RWH\\N RXHRO RgFjM RcObX RcF`NiOiUh[ R\\DSE',
	    'PoRI^I R`IlI RUU]U RUY]Y RUQU[ RYEYO RUQ]Q][ RfVl[ R[L^O RXI[M RgDjG RfVb[ RYIRO RdPhX RiOeX RcCcLfV RiMgV R]DRE RcJaX',
	    'PoRI]I R`JkJ RTU\\U RTZ\\Z Rd[l[ RTQT[ RYDYO RTQ\\Q\\[ RdLdZ R`EkEkJ RXI\\M RlWk[ RXIRO RjPcS R`E`R^[ R\\DSE',
	    'PoRI^I RTU\\U RTY\\Y Rc[i[ RTQT[ RYDYP RTQ\\Q\\[ RcIc[ RWI[L RcDgH RcU^[ RWJRO RiIcW RjVi[ RjMlT RjFfQ R_M^U R\\DSE',
	    'Po_GlG RRI]I R_TlT RTU\\U RTZ\\Z Ra[e[ RTQT[ RYEYP RTQ\\Q\\[ R_K_N ReCeK ReRe[ R_KlKlN RZK\\N RWI[L RaOjOgR RYIRO R\\DSE',
	    'PoRI]I R^SlS RTU\\U RTY\\Y Rb[e[ RTQT[ RYDYO RTQ\\Q\\[ ReGeK ReQe[ R`MjMeQ RXGWKRO RiFkK RXK^M R`E_K R\\DSE RjDaE',
	    'PoRH]H RTU\\U RTZ\\Z R_ZjZ RTQT[ RXDXO RTQ\\Q\\[ R_S_[ R_SjSj[ RWH\\N RcIfK RdDkK R_NjNeR RXGVKRO RdD]K R\\DSE',
	    'Po^HcH RgHlH RSH]H R_NcN RgNkN RgTlT R^TcT RTU\\U RTY\\Y RTQT[ RYDYO RTQ\\Q\\Z RcCc[ RgCg[ RWH\\N RXHRO R\\DSE',
	    'PocGkG RaHiH R_HfH RRH]H RcSkS R`TgT RTU\\U RTY\\Y RTQTZ RYDYO RTQ\\Q\\Z RlXk[ RXJ\\L RdJhN RXHRO R^TcSeY RiUeWgZj[ RfDjE RiJ`M RkKkNiN RgW^[ RbOdW RcCeK RfPjP R\\DSE',
	    'Po^GlG RRI]I R_QjQ R_UjU RTU\\U RTZ\\Z Rd[k[ RTQT[ RYEYP RTQ\\Q\\[ R`L`W ReKe[ R_NjMjU RkXk[ RXI[L RgJlN RXIRO RgGiL RdD_N R[DSE',
	    'Po_HkH RRI]I R_LkL R`PlP RTU\\U R`XiX RTY\\Y RTQT[ RYEYN RTQ\\Q\\[ R_D_L R`S`X R_DkDkL RZK]N RXI[L RbM^R ReRbV RXIRO RdShU Rk[g[ R\\DSE RlQk[',
	    'PoaGkG RSH]H RaMjM RaPjP RTU\\U RTY\\Y RTQTZ RYDYO RTQ\\Q\\Z RaIaP RaIjIjP RXH]M RcQ_V RXHRO RaTfXl[ RbC_J RbSjSfX_[ R\\DSE',
	    'Po^HlH RSI]I R^OlO R^SjS RTU\\U RTZ\\Z RTQT[ RXEXO RTQ\\Q\\[ RcDc[ RgEg[ R^KjKjS RWI\\N RfTlY RaU]Y RXIRO RbR`W ReD^E RkDeE R\\DSE',
	    'PoRI]I R`IjI R`NjN RTU\\U RTZ\\Z R^[l[ RTQT[ RYDYO RTQ\\Q\\[ R_R_[ R`E`N RcRc[ RgRg[ R`EjEjN R_RkRk[ RXI]N RYIRO R\\DSE',
	    'PoSE_E RTG^G RTM^M RSRkR RXXgX RXZgZ RXUX[ RYCYG RYJYM RTJ^J^M RUVhVg[ RcKkN RbDaG RbDgDgH RVP`P_SeUlV RgK`M R_RYURV RTJSO RaIhHkGkH RdO_O',
	    'Po^FkF RRH\\H R^JlJ R_PjP R_SjS RTU\\U R_WjW RTY\\Y RTQTZ RXDXO RTQ\\Q\\Z R_M_V ReCeH RhGhM RaG`MjMjV RWH[L RXGVKRN RgXkZ RbY][ R[DSE',
	    'PoRI\\I R`IiI R`QjQ R`TjT RTU[U R`WjW RTZ[Z RTQT[ RXDXO RTQ\\Q\\[ R^F^H R`O`W ReIeM R^OjOjW R^FlFlI RWI\\N RdCeF RVKRO RaK^M RhYl[ RWGUL RbY][ RkJeM]O R[DSE',
	    'PoWDeD RSFkF RaPjP R`QkQ RUX\\X RaXiX RUZ\\Z RaZiZ RUUU[ RUU\\U\\[ RaUa[ RUJhJgN RaUiUi[ RfOeSlT RSQ[Q^T RYOYSRU RgQ_U R_D_GdHlJ R]GRJ RaNdN RTO\\O^N[N R_FXIXNZMgMcNaN^NaN',
	    'PoVEjE RVIiI RVNiN Re[j[ RZU[Z R`E`R ReUhW R`UaY RUUS[ RVEVRlRk[',
	    'PoTD^D RTI]I RTM]M RY[][ RVTVX RYDYQ RfUl[ R[S\\V RfT_[ RXTYX RcMgX RjLeV RaGeU R_EkEgS RSTRZ RTDTQ^Q^V][',
	    'Po[ElE R[IkI R[MkM Rf[k[ R^U^Y RcEcQ RSEWI RfTiV RbTdX RWOTV RZTXZ R[E[QlRk[',
	    'PoSD^D R_ElE RSI]I RSM]M R_NlN RSQ]Q RSDSQ RYDYQ ReEe[ R[S[V RXTXX RVTVX R\\[Y[ RSTRZ R]R\\[',
	    'PoTD]D RTI]I RTM]M R`PiP Re[j[ RVTVX RYDYQ R[S\\V RXTYX RcC_M RSTRZ R\\[Y[ RaIlHlTj[ RTDTQ]Q]V\\[',
	    'PoSD]D RSI]I R_KkK RSM]M RSQ]Q RX[\\[ RSDSQ RYDYQ RXTXW RZS[V RfRjZ RdR`Z RUTVX RSTRY ReCeOaY ReIhW ReJcV R]S\\[',
	    'PoSD\\D RSI\\I RSM\\M RdZkZ RX[[[ RXDXQ RdFdZ RkWkZ RZSZV RXTXW RUTUX RRTRY RiE^G RSDSQ\\Q\\V[[ RlN^P',
	    'PoSE]E RSI\\I RSM\\M RSQ]Q Ra[k[ RSESQ RXEXQ RaFaZ RfCfU RlWk[ RZS[V RXTXX RUTVY RkI^M RkIkThT R\\[X[ RSTRZ R]S\\[',
	    'PoSD]D RSI]I RSM]M RSQ]Q RX[\\[ RTDTQ RYDYQ RgEgX RlDl[ RZS[V RXTYW RVTVX RSTRY R]R\\[ RaDaR`Y',
	    'PoVDiD RVGhG RVJhJ RRVlV RZNZQ R_D_L ReRe[ R_N`P RcNeO RUNTQ RZWWYS[ RVDVLjLiQfQ RZRYX',
	    'PoTD]D R^HlH RTI\\I RTM\\M RX[[[ RXDXQ ReCeN RZS[V RXTXX R`NcThYl[ R_NkNhTdX^[ RUTVX RSTRZ RTDTQ\\Q\\V[[',
	    'PoSD]D R^HlH RSI\\I RSM\\M RSQ]Q RX[[[ RSDSQ RUTUX RXDXQ R_M_X ReCe[ RZS[V RXTXX R_MkMkWhW RRTRY R\\S[[',
	    'PoSD]D RSI\\I R`LlL RSM\\M RSQ]Q RX[\\[ RSDSQ RXDXQ RhLh[ RZS[V RXTXX RUTVX RRTRY R]S\\[ RkD`F`R_Y',
	    'PoSE^E RSI]I RSM]M RaOlO RaYlY RY[\\[ RYEYQ RaEa[ RaElEl[ R[S\\V RXTYX RVTVY RSTRZ RSESQ^R][',
	    'PoTD]D RTI]I RTM]M RaMkM RTQ]Q RX[\\[ RTDTQ RXDXQ ReUk[ RhTb[ RZS[V RXTYW RbMgW RkNgW RVTVX RSTRZ R]R\\[ RaDaQ_Z R_DiEhM',
	    'PoSD]D RSI\\I ReLkL R_LgL RSM\\M RSQ]Q Rg[l[ RX[\\[ RSDSQ RXDXQ RhNh[ RbI_L RZS[V RXTXW RiGkM RlWl[ RdCbJ R_Z][ RUTVX RSTRZ R]S\\[ RbNaX',
	    'PoTD]D RTI]I RTM]M RTQ]Q RX[\\[ RTDTQ RXDXQ ReCeH RdTk[ RfT_[ RZS[V RXTXX RaLfW RiLdV RVTVY R`IcS R^HkIfS RSTRZ R]R\\[',
	    'PoVDkD RVGiG RVIiI RgPkP RTWkW RTRTW RZMZP R`P`[ R`D`K RTRkRkW R_MaO ReMgN RUMRP RVDVKlLkP',
	    'PoSD]D RSI\\I RSM\\M RSQ]Q Rf[l[ RX[\\[ RSDSQ RXDXQ R`I`L ReCeZ R`IkIkL RbW^[ RZS[V RXTXX RdQ`Z RUTVX RlWl[ RSTRZ R]S\\[ ReJcV',
	    'PoTD^D RTI]I RTM]M RTQ^Q RaQlQ RTDTQ RYDYQ RaJlJlR R[S\\V RYTYW RVTVX RSTRY RY[\\[Y[ R^R][ RkDaEaS`Z',
	    'PoTE]E RTI\\I R_KkK RTM\\M Rf[l[ RXEXQ RTETQ]Q]Z RfMf[ RlXk[ RZS[V RgDjG RWTYW RcQ_Z R\\[Y[ RVTVX RSTRY RdCdMbU',
	    'PoTD^D RTI]I RTM]M RY[][ RVTVX RYDYQ RdTjZ R[S\\V RXTYX RaNeV R`FlK RjDcJ^L RjNgTbY^[ RSTRZ RTDTQ^Q^V][',
	    'Po_ElE R_IkI R_MkM RRS\\S Rf[k[ RWOW[ RTDTL[K[M RbUbY RfEfR RhTiV ReTfX R\\FSH R^U^Z R_E_RlRk[',
	    'PoSD]D RSI]I RSM]M R_PlP RSQ]Q RX[\\[ RSDSQ RYDYQ R`HjHjP RgWl[ RZS[V RcW_[ RXTYW ReCeQiY RVTVX RSTRY R]R\\[ ReMcV',
	    'PoSD]D RSI\\I R_KlK RSM\\M RSQ]Q RX[\\[ RSDSQ RXDXQ RdVgY RZS[V RXTXW RUTVX RSTRY R]R\\[ ReCeKdPaW^[ ReKgQk[',
	    'PoTD]D RTI]I RTM]M RaNkN RTQ]Q RX[\\[ RTDTQ RXDXQ RaDkDkN R[S[V RXTYW RVTVX RSTRZ R]R\\[ RaDaR_Y ReNgTkZ',
	    'Po`DlD RTD]D RTI]I RTM]M RTQ]Q R`ZlZ RX[\\[ RTDTQ RXDXQ R`D`[ R[S\\V RXTYW RVTVY RSTRZ R]R\\[ RiHgNcU RcJkS',
	    'PoSD]D RSI\\I RSM\\M RSQ]Q RSDSQ RUTVX RXDXQ ReUk[ RgT`Z RZS[V RXTXW RbNfW RiMeW RaIdT RaIkIgT RbC_N R[[X[ RRTRY R]R\\[',
	    'PoTD]D RTI]I RTM]M RaMkM R^[l[ RYDYQ RaFa[ RhMh[ R[S\\V RXTYX RVTVY RSTRZ R\\[Y[ RjDaE RTDTQ^Q^V\\[',
	    'PoTE]E RTI\\I RTM\\M RcThT Rh[l[ RXEXQ RTETQ]Q]Z R_E_[ RcKcU RcKhKhT R_ElEl[ RZS[V RWTYW RUTVX RSTRY R\\[Y[',
	    'PoTE\\E RTI\\I RTM\\M RXEXQ RTETQ\\Q\\Z RfCfN RdTj[ RYS[V RXTXW RbOfW R`HkHjL R\\[Y[ R`OkOhUdY`[ RUTVX RSTRY R`H`R^Z',
	    'PoSD]D RSI]I RSM]M RgNkN RaZlZ RX[\\[ RYDYQ RaQa[ RaQlQl[ RdJ`N RZS[V RXTXX RVTVX ReEcL RRTRY R`ElEkN RSDSQ]R\\[',
	    'PofHiH RXMjM RXPhP RXShS Re[j[ RZXZ[ R`M`V RaDiEiH RbDaJlJkJ RdWfY R_X`Z RUWT[ RXCTH RWE^FYK RWH[JSN RXMXVkVj[',
	    'PoSD\\D R_EkE RSI\\I RSM\\M R^QlQ RX[[[ RUTUX RXDXQ ReEe[ RZS[V RXTXX RjHiN R`HaN RSTRZ RSDSQ\\Q\\V[[',
	    'PoSD]D RSI]I RSM]M R`QlQ R`ZlZ RX[\\[ RYDYQ R`I`[ RfCfZ R`IlIl[ R[S[V RXTYW RVTVX RSTRZ RSDSQ]R\\[',
	    'PoSE]E R`ElE RSI\\I RSM\\M R`SjS R`ZlZ RYEYQ R`E`Z R`KjKjS RZS[V RXTXX RVTVX R\\[X[ RSTRZ RSESQ]R\\[',
	    'PoTD]D R_HkH RTI]I RTM]M R`QkQ RTQ]Q R^ZlZ RX[\\[ RTDTQ RXDXQ ReHeZ RZS[V RXTYW RbDgF RVTVY RSTRZ R]R\\[',
	    'PoVMiM RVPgP RVShS Re[i[ RZXZ[ R_M_V RdWfY R_X`Z RUXR[ RWESI RaEeIlL R`EjEgI`K RRF]FYJSM RTH]K RVMVVjVj[',
	    'PoTD]D RTI]I RTM]M RaTgT Re[j[ RX[\\[ RVTVX RYDYQ RaMaW RaMgMgT R[S[V RXTXX RbC^L RSTRZ RaHlHlUk[ RTDTQ]Q]V\\[',
	    'PoTD]D R_ElE RTI\\I RTM\\M R^ZlZ RX[[[ RVTVX RXDXQ ReHeW RgJlO ReH^P RZS[V RXTXW RgEeJ RSTRZ RTDTQ]Q]V\\[',
	    'PoTE]E RTI\\I RaLjL RTM\\M RaSjS R][l[ RXEXQ RTETQ]Q]Z RaEa[ RaEjEj[ RZS[V RXTXX RUTVX RSTRY R\\[Y[',
	    'PobJlJ RZJ^J RVMjM RVPiP RVRiR Rf[k[ RZXZ[ RaMaU RbEbJ RbElElJ ReWgY R`WaZ RUWR[ RXGTK RYCVJ RVMVUlUk[ RSE_E^J',
	    'PoSE]E RSI\\I RSM\\M R`PkP RX[\\[ RXEXQ RbUg[ RdKfN RfGlL ReDgH RjRdX RbH^M RZS[V RXTXX RdCbJ RUTVY RSTRZ RSESQ]Q]V\\[',
	    'PoTE\\E RTI\\I RTM\\M RUTVX RXEXQ RTETQ\\Q\\Z ReDlK RfJaO ReD^K RYSZV RXTXW RhOdS`T R[[Y[ RkSfX^[ RSTRY',
	    'PoTD]D RTI]I RTM]M R_MkM RTQ]Q R`ZjZ RX[\\[ RTDTQ RXDXQ R`Q`[ R`QjQk[ R[S[V RXTYW RiHlN RVTVX RcCaM RSTRZ R]R\\[',
	    'PoSD\\D RSI[I RbJlJ RSM[M RSQ\\Q Re[i[ RTDTQ RXDXQ RaGa[ RiCi[ RYSZV RWTXW RdNfT RbD]N RUTUX R[[W[ RSTRY R\\S[[',
	    'PoSD\\D RSI\\I RSM\\M R^QlQ RSQ\\Q RX[[[ RSDSQ RXDXQ R`H`Q R`HjHjQ RgWl[ RcV^[ RZSZV RWTXX ReCeRhY ReQbY RUTUX RRTRY R\\R[[ ReMdV',
	    'PoSD]D RSI\\I RSM\\M R_OkO RSQ]Q RX[[[ RSDSQ RUTVX RXDXQ R_H_O R_HkHkO RcV_Z RZS[V RXTXW R_QaUdXj[ RSTRY R\\S[[ ReCeQdW',
	    'PoTD]D RTI\\I RTM\\M Rc[i[ RX[[[ RVTVX RXDXQ RcIc[ RjWi[ RdDgG RdS^Z RZS[V RXTYW RjGdT RjMlT RjEgN RSTRZ R_L^T RTDTQ]Q]V[[',
	    'PoTD]D RTI]I RTM]M RTQ]Q Rb[k[ RX[\\[ RTDTQ RXDXQ R_G_K RbKbZ ReCeG R_GlGlK RlWk[ R[S\\V RXTYW RkNaS RVTVY RSTRZ R]R\\[',
	    'PoSD\\D R`HlH RSI[I RSM[M Ra[k[ RUTUX RXDXQ RaLa[ ReJeW RlXk[ RZSZV RWTXX RaC^L RjL_Q RkLjUgU RX[[[X[ RRTRY RSDSQ\\Q\\V[[',
	    'PoSD]D RSI]I RSM]M RSQ]Q RhRlR R`ZlZ RX[\\[ RSDSQ RYDYQ R`D`[ RhDhQ R`DlDl[ RZS[V RXTYW RVTVX RSTRY R]R\\[ RdDdNcS',
	    'PoSD[D RSI[I RSM[M RhVlV RXDXQ RWTWW R_F_U RgFg[ RgFlFlU RbW^[ RYSZV R[[W[ RdD^E RUTUX RdGcT]U RRTRY RSDSQ\\Q\\V[[ RcObW',
	    'PoTI]I RbIkI RVKjK RVOiO RVRiR Rf[j[ RTDTI RZWZZ RTD]D]I R`K`U RbDbI RbDkDkI ReWgX R_WaY RUWRZ RVKVUlUj[',
	    'PoTD]D RTI\\I R_IlI RTM\\M R_PlP RTQ]Q RTDTQ RVTVX RXDXQ RiIi[ RZS[V RXTXW RaCbH RjChH R\\[X[ RSTRY R]R\\[ RbIbSaX',
	    'PoSD]D RSI\\I RSM\\M Rg[l[ RUTVX RXDXQ RhDh[ RlWl[ RgPkT RcP_S RbW^[ RZS[V RXTXX R_HaM RkGhM R[[X[ RSTRZ RSDSQ]Q]V\\[ RcDcRbW',
	    'PoSE]E R_ElE RSI\\I R_MkM RSM\\M R`SkS R^ZlZ RX[[[ RXEXQ ReOeZ RZS[V RXTXX RiHkN RcHaM RdEbK RUTUY RSTRZ RSESQ]Q]V\\[',
	    'PoTE\\E R^HkH RTI[I RTM[M R]PcP RXEXQ RTETQ\\Q\\Z RgDjG RgTaZ RYSYUXW RjMfW RlWk[ R[[X[ RUTUX RSTRY ReCeJeMgY R`K`Q_W',
	    'PoUGiG RSKlK RXQfQ RXSfS Re[h[ R[XZZ R`N`V RdWfY R_X`Z RXDYF RcKgN RVXSZ RfDdF RXNfNlP R_C]IXNRQ RYMXViVh[',
	    'PoSD]D RSI\\I RbIiI RSM\\M RSQ]Q RcThT RX[\\[ Rh[l[ RSDSQ RVTVX RXDXQ R_E_[ RcMcV RcMhMiT R_ElEl[ RZS[V RXSXW RSTRY R]R\\[',
	    'PoTE]E RTI\\I RTM\\M R`QgQ R`VgV Re[j[ RUTVX RXEXQ RTETQ]Q]Z R`L`W R`LgLgV RZS[V RWTYW RbC^K RSTRY R\\[Y[ R`HlHlSj[',
	    'PoSE]E RSI\\I R`MjM RSM\\M R_SjS R^ZlZ RUTVY RXEXQ ReMeZ RfGlL ReCgH RcG^L RZS[V RXTXX ReCbI R[[X[ RSTRZ RSESQ]Q]V\\[',
	    'PoSD]D R`IkI RSI\\I RSM\\M R^OlO RSQ]Q Rg[l[ RSDSQ RXDXQ ReCeO RgOg[ RlXl[ RZS[V RXTXX RaD^L R_Z][ RUTVX R[[X[ RSTRZ RbO`X R]S\\[',
	    'PoSD\\D RSI\\I RSM\\M R^NlN Rg[l[ RXDXQ ReCeN RgNg[ RlWl[ RYSZV RWTWW RjEhK R_Z][ RUTUX R`FaK R[[W[ RSTRY RTDTQ\\Q\\V[[ RbNaX',
	    'PoSD\\D RSI[I RaKiK RSM[M RaPiP RSQ\\Q RX[[[ RTDTQ RXDXQ RaEaW R_EkEj[ RYSZV RWTXW RUTUX RSTRY R\\S[[ ReV_W RiVbW',
	    'PoTD]D R^HlH RTI\\I RTM\\M RX[\\[ RVTVX RXDXQ RfVl[ RaMdP RhL`T ReFcJ`M RZS[V RXTYW RjQcX^[ ReDdI RSTRZ RTDTQ]Q]V\\[',
	    'PoTD]D R^GlG RTI\\I RTM\\M RX[\\[ RVTVX RXDXQ ReCeG RhJlN RaJ^N RZS[V RXTXW RaOdUhYl[ RhOeUaY][ RSTRZ RTDTQ]Q]V\\[',
	    'PoSD\\D R`IkI RSI[I RSM[M R^OlO RSQ\\Q RX[[[ RSDSQ RXDXQ ReCe[ RhTlX RbT^Y RYSZV RXTXW ReMiV ReMaV RUTUX RaE_L RSTRY R\\S[[',
	    'PoTD]D RTI\\I RaLjL RTM\\M RTQ]Q R_ZlZ RTDTQ RXDXQ R_D_[ R_DlDl[ RZS[V RXTXW ReGeOiV RVTVX RSTRZ R[[X[ ReJgS R]R[[ ReJdT',
	    'PoTD]D RTI\\I RTM\\M RaZjZ RX[[[ RVTVX RXDXQ RaRa[ RaRjRj[ RZS[V RXTXX RbGcKhPlQ RcC_J RbGiFgKbP^R RSTRZ RTDTQ]Q]V\\[',
	    'PoTD]D RTI\\I RTM\\M RTQ]Q RTDTQ RVTVY RXDXQ RgDg[ RlDl[ RZS[V RXTXX RiKjQ RSTRZ R_K^Q R[[X[ RdKdP R]R\\[ RbDbR`Y',
	    'PoSDZD RSIZI RSMZM RgMkM RcWlW Rc[l[ RV[Y[ RWDWQ RcKcW RhEhW R^VbZ RXSXV R`S]Z RWTVVUX R]P_W RRTRY RkDbF R\\EaE\\MaM_X RSDSQ[QZVY[',
	    'PoTE[E RTI[I R]IlI RTM[M R]PeP RX[Z[ RVTVX RXEXQ RTETQ[Q[Z RcKcW RiDkF RgVb[ RWTXW RjMfX RlWk[ RSTRY RgCgOiZ R`K`S^X',
	    'PoTD[D R^ElE RTI[I R^JcJ RTM[M R^RcR RgRkR RUTVX RXDXQ R^J^R RgEg[ RgJkJkR RYSZV RWTXW R[[X[ RSTRZ RTDTQ\\Q\\V[[ RcEcQbX',
	    'PoTE\\E RTI[I R_JkJ RTM[M RbWhW Rg[k[ RUTUX RXEXQ RTETQ\\Q\\Y RbQbX RbQhQhV R_DkDkJ RYSZV RWTXW R[[X[ RSTRY R_NlNlWk[ R_D_R^Y',
	    'PoSD\\D R]GkG RSI\\I RSM\\M R^QjQ RRTRY RXDXQ R^K^Q RdCd[ R^KkKkQ RfTlY RbT]Y RZSZV RWTXX ReQgU RcQaW RUTUX RX[[[X[ RSDSQ\\Q\\V[[',
	    'PoTD\\D R^HlH RTI\\I R`LjL RTM\\M R`SiS RX[[[ RVTVX RXDXQ R`DjDjL R^O^PkOkQ RZS[V RXTXW RaTeXk[ RiTeX_[ RSTRY RTDTQ]Q]V\\[',
	    'PoTD\\D R]HlH RTI\\I RTM\\M Rb[k[ RUTVX RXDXQ RaCbNcZ RYSZV ReDhE RiJeN RhQdS RjOfR RlWl[ RWTXW RkShVdX RX[[[X[ RSTRZ RTDTQ\\Q\\V[[ RaJ_X',
	    'PoTE\\E RTI\\I R_KjK RTM\\M R_PjP RXEXQ RTETQ\\Q\\Y R_F_[ RdCeF R_FjFjP ReUkZ RYSZV RWSXW RdPgX R[[X[ RkSfU RUTVX RSTRY ReY^[',
	    'PoSD]D R_HkH RSI\\I R_MkM RSM\\M R_PkP R^UlU RXDXQ RSDSQ]Q]Z R_D_L RePe[ R_DkDkL RZS[V RXTXX R\\[Y[ RVTVX RRTRY',
	    'PoTD\\D RTI\\I R`KiK RTM\\M R^PkP Ra[d[ RX[[[ RXDXQ ReKe[ RdClK RdE]K RZS[V RXTXW RiSkY R`S^X RVTVX RSTRZ RTDTQ\\Q\\V[[',
	    'PoTE]E RTI]I RaJlJ RTM]M RaRlR RaVlV Ri[l[ RVTVX RYEYQ RTETQ^Q^Y RaMa[ RaDaJ RaDlDlJ RaMlMl[ RZS[V RXTYW RY[\\[Y[ RSTRY',
	    'PoTD]D RTI\\I RTM\\M R`QjQ Rg[k[ RX[[[ RVTVX RXDXQ R`K`Q RgQgZ R`KjKjQ RlWk[ RaW^[ RZS[V RXTXW RbEhEkJ RaF_K RSTRY RcQaY RTDTQ]Q]V\\[',
	    'PoTD\\D R^IkI RTI\\I RTM\\M RaRjR RX[[[ RVTVX RXDXQ RgHgNkMkN RhElI RbP^U RZS[V RXTXX RbSfYl[ RbC`H RbH`N]N RiSdX^[ RSTRZ RTDTQ]Q]V\\[',
	    'PoTE\\E RTI\\I R]LlL RTM\\M R^[a[ RVTVX RXEXQ RTETQ\\Q\\Z RaEa[ RiEkH RhVc[ RYSZV RXTXW RkOgX RlWl[ R[[X[ RdD]E ReR]S RSTRY RgCgNgQhZ',
	    'PoTD]D RTI\\I R_JkJ RTM\\M R_NkN R^QlQ Rd[i[ RX[\\[ RVTVX RXDXQ R_F_N ReCeN R_FkFkN RZS[V RXTYW RbQaUjTi[ RSTRZ RTDTQ]Q]V\\[',
	    'PoTD]D R_GlG RTI]I RTM]M R^NlN RTQ]Q R_TlT RX[\\[ RTDTQ RVTVY RXDXQ ReCeG ReNe[ R[S\\V RXTYX RaIbM RjIhM RSTRZ R]R\\[',
	    'PoTD\\D RTI\\I R`IkI R]JhJ RTM\\M R`OkO R^SlS RX[[[ RXDXQ RXTXW RfVk[ RgFlJ RdV_Z RZS[V RdRgX RcC`I RaK^Q RVTVX RSTRZ RTDTQ\\Q\\V[[ RdOdW',
	    'PoTD\\D RTI\\I R`JhJ RTM\\M R]NlN Ra[d[ RVTVX RXDXQ ReNe[ R_QaS RfTkY RkQhS RZSZV RXTXX ReOhW RcV]Y RaC`L R[[X[ RSTRZ RaFiFgN RTDTQ\\Q\\V[[',
	    'PoTD]D RTI\\I R`JlJ RTM\\M R`NkN R`TkT R`ZlZ RX[\\[ RVTVX RXDXQ RaHa[ RfGfY RZS[V RXTYW RbD]N RgDfI RSTRZ RaE`L RTDTQ]Q]V\\[',
	    'PoTE]E RTI\\I RbIiI RTM\\M RbMjM RcViV Rh[l[ RXEXQ RTETQ]Q]Z RcQcX RfFfM RcQiQiV R_DlDl[ RYS[V RXTXW RUTVX RSTRY R[[Y[ R_D_S^Z',
	    'PoTE\\E RTI\\I R`KiK RTM\\M R^OlO R`ZjZ RXEXQ RTETQ\\Q\\Z R`S`[ ReKeS R`SjSj[ ReDlK RdD^K RYSZV RWTXW R[[X[ RUTUX RSTRY',
	    'PoTD]D RTI\\I RfIlI RTM\\M ReQlQ RX[\\[ RVTVX RXDXQ RiHi[ RZS[V RXTXW RjDhJ RdP^Q RSTRY RcCbJ RTDTQ]Q]V\\[ R^IcIbSaY',
	    'PoSD\\D RSI\\I R_IkI RSM\\M R_MkM R_SdS Rf[l[ RUTUX RXDXQ R_D_M R_O_[ RgOg[ R_DkDkM RlXl[ RZSZV RWTXX RkRfT R[[X[ RdZ_Z RRTRY RSDSQ\\Q\\V[[',
	    'PoTD]D RTI\\I RaLhL RTM\\M R_PiP R`WhW Rf[k[ RX[\\[ RVTVX RXDXQ R`R`X RdKdV RhRhX RZS[V RXTXX RaC^J RaI_N RSTRZ R`GlGlUk[ RTDTQ]Q]V\\[',
	    'PoTD\\D R]GlG RTI[I RTM[M RXDXQ RdCd[ RfSlY RdOgT RhMkP RbT]Y RZSZV RXTXW R`McOaU RVTVX RSTRZ R`J^Q RhJgP RX[[[X[ RTDTQ\\Q\\V[[',
	    'PoTD\\D RTI\\I R`LjL RTM\\M R^PlP RX[[[ RXDXQ R^G^J ReCeG R^GlGlJ RZS[V RXTXW RiTlY R`T^Y RVTVX RePe[a[ RSTRZ RTDTQ\\Q\\V[[',
	    'PoSD\\D RSI[I R_IkI RSM[M R_NkN RSQ\\Q R^RlR RW[[[ RSDSQ RXDXQ R_D_N ReDe[ R_DkDkN RfTlY RcS^Y RYSZV RWTXW RUTUX RSTRY R\\R[[',
	    'PoSD\\D R_FlF RSI\\I RSM\\M RSQ\\Q R`WfW RSDSQ RXDXQ R`Q`X R`QfQfV RYS[V RWTXX RdHkL ReHbK^L RUTUX ReCdJ R[[X[ RRTRY R\\S[[ R^NlN RjNj[g[',
	    'PoSD\\D R^HlH RSI[I RaLiL RSM[M RaPiP R^TlT RXDXQ RaCaT RiCiT RhWl[ RbW^[ RZSZV RXTXW RX[[[X[ RUTUX RSTRY RSDSQ\\Q\\V[[',
	    'PoTD]D RTI\\I RaJhJ RTM\\M Rb[h[ RX[[[ RXDXQ RcTc[ RhXh[ ReCjI RdC^J RZS[V RXTYW RjUlZ RcRgS R_MiMgQ R_U^Z RVTVX RSTRZ RTDTQ]Q]V\\[',
	    'PoTD\\D R^HcH RgHlH RTI\\I RTM\\M R^McM RgMkM RgTlT RXDXQ RgCg[ RZS[V RaW^[ RXTXW RUTVX RcCbT]T RX[[[X[ RSTRZ RTDTQ]Q\\V[[ RbPbX',
	    'PoTD\\D RTI\\I RTM\\M R_TkT RX[[[ RXDXQ R_P_T R_PkPkT RhWl[ RZS[V RXTXW R`FdJlM R_EjEeJ^M RVTVX RSTRY RTDTQ]Q]V[[ ReMeZ Ra[jY',
	    'PoTD]D RTI\\I RaJiJ RTM\\M R_TjT RX[[[ RXDXQ R_N_S R_NjNjS RfVk[ ReCkJ RdV_Z ReC^J RZS[V RXTXX ReJeTgX RVTVX RSTRZ RTDTQ]Q]V\\[ ReNdW',
	    'PoTE\\E R`HkH RTI\\I R`LkL RTM\\M R`XhX Rg[j[ RX[[[ RXEXQ RTETQ\\Q\\Z R`E`L R`S`X R`EkEkL RbN^R ReSbV RYSZV RWTXX ReShU RUTVX RSTRY RaPlPlUk[',
	    'PoSD\\D R]ElE RSI[I RSM[M R^NkN R]RlR RW[[[ RXDXQ R^H^N RbEbN RgEgN R^HkHkN RYSZV RWTWW RUTUX RcPbS_VkZ RiTeY][ RRTRY RSDSQ\\Q\\V[[',
	    'PobFlF RVNiN RVQgQ RVThT Rf[j[ RYHYM RZXZ[ R_N_V RVNVVkVj[ RdXeY R_X_Z RUXR[ RRH^H]K RcD_H RbFfKlM RiGfJ`M RTD^DYF RXHVJRL RVF]H',
	    'PobElE RbIlI RTK^K RbMlM RUT_T R\\[_[ Rf[k[ RTFUSU[ RXOXZ R[O[Z RTF^F^K RUO_O_[ RdUdY RgEgR RYCZF RgTgX RhTiV RaUaZ RbEbRlRk[ RTPRZ',
	    'PoTE\\E RTI\\I RTM\\M RfQjQ Ra[i[ RUTUX RXEXQ RbTb[ RiXh[ RcSeV RcJ_N RfLaQ RYSZV RWTXW RiTkY RaJhM RaC_I RdGbL RTETQ\\Q]W]Z RhGeN R[[X[ RSTRY R`GkGjQ',
	    'PoTD]D RTH\\H RTL\\L R`MlM RTQ]Q RaUlU RW[[[ RTDTQ RVTVX RXDXQ R`EaSa[ RePe[ RhPh[ R`HlHlM RaPlPl[ RZS[V RXTXW RSTRZ R\\R[[ R`O_Y RkD`E',
	    'PoTD\\D R^HlH RTI[I RTM[M RXDXQ R_L_[ RgMgV R`ObQ R`TbU RYSZV RWTXW RaCbG RiCgG RUTVX R[[W[ R_LcLc[a[ RkKk[h[ RSTRY RTDTQ\\Q\\V[[',
	    'PoSD\\D RSI[I RSM[M R]OgO RUTUX RXDXQ RdGd[ RlXl[ RhHlI RkFiH RZSZV RXTXW RiMkN RkQhSlU RiZk[ RbH]J RX[[[X[ RRTRY R`I`R^Y RgQiZ R^EhEiM RSDSQ\\Q\\V[[',
	    'PoSD]D RSI\\I R_JkJ RSM\\M R_NkN R_QkQ R_VjV R^ZlZ RXDXQ RSDSQ]Q]Z R_F_N ReQeZ R_FkFkN RdCdG RZS[V RXTXX R\\[X[ RVTVY RSTRZ',
	    'PoTE[E RTI[I RbJlJ RTM[M RbMjM RbQkQ RTQ[Q RaUlU Re[l[ RTETQ RXEXQ RfCfY RbFjFjM RYSYV RUTUX RSTRY RZ[W[ R]FaF]NaN_X R]R_WcZj[ R[SZ[',
	    'PoTD\\D RTI\\I RTM\\M R^PlP RaZkZ RXDXQ RbSb[ RdCdO RaUkUk[ RbS]X RZS[V RXTXX RcP`V RVTVX RX[[[X[ RSTRZ RjD^G RjH_J RkK^M RTDTQ\\Q\\V[[',
	    'PoSD\\D RSI[I R`JiJ RSM[M R^QdQ R^UdU Rh[k[ RX[[[ RXDXQ R^M^[ RgNgW RkMk[ RYSZV RWTWW ReDhGlI RdCaH]I RUTUX R^MdMd[a[ RRTRY RSDSQ\\Q\\V[[',
	    'PoTD\\D R`HjH RTI[I R`LjL RTM[M R^PlP RTQ\\Q ReTkT Rd[l[ RTDTQ RXDXQ R`D`L RfPfZ R`DjDjL RYSZV RWTXX R`TbY R[[X[ RUTUX R`R^Z RSTRY R\\R[[',
	    'PoSE[E RSI[I R_JjJ RSM[M R_OjO Ra[d[ RUTUX RWEWQ RSESQ[Q[Z R_F_O ReOe[ R_FjFjO RfTlZ RbU]Z RjQgU RYSYV RWTXW RePhW RdCdF R^RcR`W R[[X[ RSTRY',
	    'PoTD[D RTI[I R^KcK ReLkL RTM[M R^PcP R^UcU RWDWQ R^D^[ R^DcDcK ReDkDkK RhWl[ RYSZV RiVd[ RWTXW ReQiY RePkPgX RW[Z[W[ RUTUX RSTRZ RTDTQ\\Q[VZ[',
	    'PoTD\\D RTI[I R_LjL RTM[M RTQ\\Q RTDTQ RUTUY RXDXQ R_D_L RjDjL RfLfPkOkP RYSZV RcQ^V RgDaJ RWTXX RaTeXl[ RaFfH RbLaP]Q RaTiTeX^[ RW[[[W[ RSTRZ R\\R[[',
	    'PoTD\\D R_EcE RTI\\I RTM\\M R`NiN R^SkS RVTVX RXDXQ RfVk[ RhJlN RlIhJ RdV_[ R_HaJ]N RZS[V RXTXW ReNeSgX RiEfFiM RdSbY RcF`M RfDhK RSTRY R[[X[ RTDTQ]Q]V\\[ RdPdW',
	    'PoSE\\E RSI[I RSM[M RbOeO R^SlS RX[[[ RUTUX RWEWQ RSESQ\\Q\\Z ReQe[ RfHfN ReRlY ReI`N R_IkIiM RdS]Y RYSZV RWTXW RbFhI R_EjEdG RSTRY',
	    'PoSD]D RSI\\I R_JkJ RSM\\M R_OkO RSQ]Q RbZiZ RX[\\[ RSDSQ RXDXQ R_E_O RbRbZ ReEeO RiWiZ R_EkEkO RdQgT RZS[V RXTXX RjSlX RUTVX RSTRZ R_S^Y R]S\\[',
	    'PoTD]D ReHjH RTI\\I RTM\\M RcWiW Rh[l[ RVTVX RXDXQ R_N_[ RcQcY ReHeN RcQiQiW RaDjDjN RaD`NlNl[ RZS[V RXTXX RSTRZ R\\[X[ RTDTQ]Q]V\\[',
	    'PoTD]D RTI\\I RTM\\M R_NkN R_RkR R^VlV RX[\\[ RVTVX RXDXQ R_I_R ReIe[ R_IlIlR RZS[V RXTXW R_DaH RdDeG RkDiH RSTRZ RTDTQ]Q]V\\[',
	    'PoSD\\D RSI[I R^KjK RSM[M R^PjP RfWkW RXDXQ R^F^P R^FkFkP RePd[lZl[ RaV\\[ RYSZV RWTWX RjTkW RhReW RcP`Y R[[W[ RUTUX RSTRZ RSDSQ\\Q\\V[[ RcCdFdKbU',
	    'PoSE\\E RSI[I R_JbJ RgJkJ RSM[M R_NkN R^RjR RX[[[ RXEXQ R_F_N ReCeR RgFkFkN RYSZV RWTWX R`SdXk[ RjSeX^[ RbF^F RUTUY RRTRY RSESQ\\Q\\V[[',
	    'PoUHiH RVJhJ RRMlM RXPfP RXTfT RSESH R[X[[ R_P_W RSEkEkH RdXeY R_X`Z ReGdMhR RVXU[ RXRfQlS RYOXWiWh[ RZGZMVRRS Re[h[e[',
	    'PoSD[D RSI[I R]IlI RSM[M RSQ[Q RhRlR RSDSQ RWTWW RXDXQ ReEe[ RhKhR RdRiWlX RdR^X R^MaLaQ]Q RlLhN RUTUX RZ[W[ RRTRY RYSZUZ[ RiD_E',
	    'PodEjE R[GlG RVNiN RVQhQ RVThT Rf[j[ RSDSM RZXZ[ R_D_G R_N_V RdXeY R_X_Z RUXR[ RSDZDVHYI RYLVL R_I\\J RVNVVkVj[ RdCdKaJ RgK]M',
	    'PoTD\\D RTI\\I RTM\\M RTQ]Q R_UlU R_ZlZ RX[[[ RTDTQ RUTVY RXDXQ R_E_M R_P_[ RePeZ R_PlPl[ RZS[V RXTXX RbIdL]M ReElElNhN RdD_E RSTRZ RhEfM R\\R[[',
	    'PoSD\\D R_FlF RSI\\I RSM\\M R_PlP RSQ\\Q Re[i[ RX[[[ RSDSQ RXDXQ R_K_N ReMfP R_KlKkN RdCeF RZS[V RWTXW RbGbJ RhGgK R`Z^[ RUTUX RcTjTi[ RRTRY RcPaX R\\S[[',
	    'Po\\GkG R[KlK RTKYK R]NjN R^PiP RTRYR R^SiS RV[Y[ Rf[j[ RTDYDY[ R`X`Z RdNdU RfWhX RfJkO R]W\\[ RaJ[O RdXdZ R^D^F RfGgL RhCgF RcD_L^VkVj[ RTDTSR[',
	    'PoTD\\D RTI\\I RTM\\M RTQ\\Q R^RkR R^UlU RTDTQ RXDXQ RfVjZ R_LdN RhOlR RiFkH RcJaL RgKaQ RYSZV RWTXW ReTfX RdFdI R`E^J RdWaZ^[ RUTUX ReQcX R[[X[ RSTRZ R\\R[[ RiD`E',
	    'PoSD\\D R^FlF RSI[I RSM[M R_NjN R_RjR R^VlV RXDXQ R_J_R RbCbH ReRe[ RgCgH R_JjJjR RYSZV RWTXW RUTUX RX[[[X[ RRTRY RSDSQ\\Q\\V[[',
	    'Po`EkE RTE]E RTI\\I R`IkI RTM\\M R`MkM RY[\\[ RXEXQ RTETQ]Q]Y RcUcY RfEfQ RZS[V RhSjV RXTXW RfTfX RUTVX RSTRY R`U_Z Rk[g[ R`E`QlRk[',
	    'PoSD\\D R`HjH RSI\\I R`KjK RSM\\M RSQ\\Q R^SlS RW[[[ RSDSQ RXDXQ R_N^Q R`D`K R`DjDjK R_NkNkQ RYS[V RWTXX RdPbT RUTUX R`WjY RiUeY^[ RSTRZ R\\S[[',
	    'PoTE\\E R_ElE RTI\\I RTM\\M RbMjM RbQjQ Rd[f[ RVTVX RXEXQ RTETQ\\Q\\Z RbIbQ RfQf[ RfEfI RbIjIjQ RYSZV RXTXW RjTlY RcTaY R[[X[ RSTRY R_E_S^[',
	    'PoSD]D RSI\\I R`KhK RSM\\M RgOkO RSQ]Q R`WiW RX[[[ Rg[k[ RTDTQ RXDXQ R`G`L R`S`W RhHhK RiTiX RbP^T RbD^I RZS[V RXTXW RdM_O RcY_[ RUTVX RSTRZ RdGdM RdSdY RaFlFkN RaRlRk[ R\\R[[',
	    'Po_EjE RTD\\D RTI[I RTM[M RTQ\\Q R_UjU R^ZkZ RTDTQ RXDXQ R_P_U ReMeZ R_PjPjU RhWl[ RYSZV RdFeH RWTXW R^H_J RaFeKlN RjFfJ^M RUTUX R[[X[ RSTRY R\\R[[',
	    'PoTD\\D RTI\\I R_MlM RTM\\M RTQ]Q RX[[[ Ri[l[ RTDTQ RUTVY RXDXQ R_HlHlM RgPlPl[ RYS[V RcQ`T RdU`Y RjQgT RkUgY RXTXX RSTRZ R_PePe[b[ R\\R[[ R_E_S^[ RkD_E',
	    'PoSD\\D R^FlF RSI[I RaIiI RSM[M R^QjQ R^TkT R_WjW R][l[ RW[[[ RUTUX RXDXQ R^L^Q RaCaI ReIeZ RiCiI R^LjLjQ RZSZV RWTXX RRTRY RSDSQ\\Q\\V[[',
	    'PoTE]E RRG_G RSJ^J RRL^L RVOhO RVRgR RVTgT Re[i[ RYCYG RYJYN RZXZ[ R_O_V R`FiFjM RdXeY R^X`Z RUXS[ R[G[J RaHgK RbL_M RlKkNlM RVOVVjVi[ RdDcK',
	    'PoSD\\D R]GlG RSI[I RSM[M RUTUX RXDXQ ReCeRgWl[ R^PbS R^JbM RgPkT RgJkM RaP]T RjIfM RcV^[ RjPgS RbI]N RYSZV RWTWW RdRaY R[[W[ ReNfV RRTRY RSDSQ\\Q\\V[[ RdMcU',
	    'PoSE[E R^FlF RSI[I RSM[M R^PkP R`UeU Rf[l[ RUTUX RWEWQ RSESQ[Q[Z R`Q`[ RbFbP RdCdF RgQg[ RgFgP R^KkKkP RlXl[ RYSXUWW RZ[W[ RkSfU RdZ_Z RSTRY R^F^T\\[',
	    'PoTE\\E RTI\\I RTM\\M R`UkU R`ZkZ RXEXQ RTETQ\\Q\\Z R`Q`[ R^DdDdM RdNeQ R`QkQl[ RfDlDlM R_FaI RgFiI RYSZV RWTWX RdJ^L RlIfL R[[X[ RUTUX RSTRY',
	    'PoTD\\D R_HkH RTI\\I R_MkM RTM\\M R^UkU RVTVX RXDXQ R_D_L ReUe[ R_DkDkL R_PcR RhWkZ RiRlU ReDeL`P RhObT RaW^Z RZS[V RXTXX R[[X[ RSTRZ RTDTQ]Q]V\\[',
	    'PoTE\\E R_FkF RTI\\I R^JlJ RTM\\M R`PjP R`SjS R^WlW RXEXQ RTETQ\\Q\\Z R`M`S ReSe[ R`MjMjS RYSZV RWTXX ReCeF RhGhJ R[[X[ RUTVX RSTRY',
	    'PoREkE RVJhJ RVMhM RROlO RWSgS RWUgU Re[h[ RVHVM RZCZF R[Y[[ R_Q_W RdCdF RVHhHhM RXQXWiWh[ RVYT[ R_Y_[ ReOgQlS R\\MWQRS',
	    'PoTD]D RbGlG RRG_G RSI^I RVOhO RVQhQ RVThT Re[i[ RYCYI RZXZ[ R_O_V RdXeY R_X`Z RVXS[ RWJRO RbGfKlM RcCaH RiGeK_N RVOVVjVi[ RVL]K\\NYN',
	    'PoTD\\D R^HkH RTI\\I RTM\\M RUTVX RXDXQ RYSZV RdJjQ RgMkLlN RhEkI RdK^Q RWTXX RhIhN R_I^M RcCaH RdQ_S RfS`V RjUhW`[ RX[[[X[ RSTRY RcM]M RTDTQ\\Q\\V[[',
	    'PoTE\\E R^ElE RTI\\I RTM\\M R_MkM R`PkP R^TlT Ra[e[ RXEXQ RTETQ\\Q\\Z R_H_M RcEcM ReTe[ RgEgM R_HkHkM RiVlZ RaV]Z RYSZV RWTXX R[[X[ RUTUX RSTRY',
	    'PoTD\\D RTI[I RTM[M R_QkQ RTQ\\Q Rb[h[ RTDTQ RUTVY RXDXQ R_F_P RcTc[ RhXh[ R_FkFkP RcIhIcN RdCcF RYSZV RWTXX RiUlZ RdHaKhM RbRfT R_U^Z R[[X[ RSTRZ R\\R[[',
	    'PoTD\\D R_DkD RTI\\I RTM\\M RcMiM RTQ\\Q RaVeV RgVkV R_ZlZ RTDTQ RUTVY RXDXQ R_D_[ RaPaV RcHcM RePeV RgPgV RcHiHiM RkPkV RYSZV RWTXX RaPeP RhPkPgP RX[[[X[ RSTRZ R\\R[[',
	    'PoSD\\D R_HjH RSI[I RSM[M R^PkP R^TkT R]WlW RXDXQ R_E_L`S ReEe[ R_LjLiS R_EkEkL RYSZV RWSXW RUTUX R[[X[ RRTRY RTDSQ\\Q\\U[[',
	    'PoSE^E RRG_G RZJ_J RTK^K RiNlN RSNYOiP RVRhR RVThT Rf[j[ RYJYN RZXZ[ RYCYG[I R_P_V R`FiFiM RlKlN RdXeY R_X`Z RUXS[ RaIfK RbM_N RVISJ RVPVVkVj[ RdCcL',
	    'PoTE\\E RTI\\I RTM\\M RcNfN RbYhY Rh[k[ RVTVX RXEXQ RTETQ\\Q\\Z R^P^[ RbUbZ RfGfN RbUhUhY R^PkPk[ RfQiT RcQ`T R^HlHiK RYSZV RWSXW RbFgH R_DkEeG RfI]M R[[X[ RSTRY',
	    'PoTE\\E RdEkE RTI\\I RaLgL RTM\\M R_ZlZ RWTWX RXEXQ RaSbW RdCdH RdQdZ RgQgZ R_IkIjK RdJdOjNjO RSTRZ RZ[W[ RjSjW RTETQ[QZ[ R_I_Q]Z',
	    'PoTD\\D R]FlF RTI\\I R^KlK RTM\\M R]OlO RTQ\\Q R^SlS R^WlW RTDTQ RUTVY RXDXQ R`K`S RbCbI ReKe[ RhChI RiKiS RYSZV RWTXX RX[[[X[ RSTRZ R\\R[[',
	    'PoSD[D RSH[H RSM[M R^QbQ RdTjT RW[Z[ RWDWQ R^MbMbP ReVjZ RfGkL RiDfG RkGhI RjVeZ R^GaH]L RYSZU RWTWW ReChI R^DcD`J Ra[^[ RUTUX ReLiLjQiQ RSTRY ReMdQ RSDSQ[Q[VZ[ R]P]UbTa[',
	    'PoSD[D R]HlH RSI[I R^KjK RSM[M R^TkT RW[Z[ RXDXQ R^N^XbWb[ RdCd[ RgWg[ R^EjEjK RkNk[ RYSZV RWTWW RUTUX RgNgRkR RjVgW RRTRY RSDSQ\\Q\\V[[ RbNbR^R]Z',
	    'PoTE\\E R_FjF RTI[I R^IkI RTM[M R^McM RfMkM ReQlQ R]TlT Rf[l[ RXEXQ RTETQ[Q[Z RaKaQ RdCdI RgTg[ RhKhQ RlXl[ RYSXUXW R`Y\\[ RUTUX RSTRY RbT`Y R[[X[ RcQ]Q',
	    'PoTD\\D RTH[H R]IlI RTL[L RTQ\\Q ReRlR RdWlW RV[Z[ RTDTQ RXDXQ RdCdO ReSdX RiOi[ R_D`G RdHhLlM RiDhG R^SbV][ RdH_M RYSZV RWTWW R_QdQ`Y R`O]U RUTUX RSTRZ R\\R[[',
	    'PoTD\\D R^HkH RTH[H RbKjK RTM[M RaMkM RfWlW R`[b[ Rg[i[ RXDXQ RbUb[ RiUi[ R^DkDkH RYSZV RfSlSiU RWTXW RUTUX RfMfQcP R`SeScV_W RZ[W[ RSTRY RTDTQ\\Q[VZ[ R^D^R]Z',
	    'PoSDZD R\\FdF RSIZI RSMZM R]NcN R][`[ RV[Y[ RUTUX RWDWQ R]I]M R`S`[ R]IcIcM R_C`F R]QcQ`S RXSYV RVTWX RgRk[ RiQeZ RfNiX RjMgX RfJhW RSTRY RgCdO RSDSQ[QZVY[ RfIkIhU RdU\\V',
	    'Po^DlD RTD\\D R_HcH RTI\\I RTM\\M R_NcN RTQ\\Q R_UkU R_ZkZ RTDTQ RXDXQ R_H_M R_Q_[ RcDcQ ReQeZ RgDgQ R_QkQk[ RYSZV RWTXX RUTUY R[[W[ RSTRZ R\\R[[ RgHkHkMfN',
	    'PoSD\\D RSI[I R^IcI RfIkI RSM[M R_OkO RSQ\\Q R_SkS R]WlW RW[[[ RTDTQ RXDXQ R^D^I R_L_S R^DcDcI ReLe[ RfDfI R_LkLkS RfDkDkI RYSZV RWTWW RUTUX RSTRY R\\S[[',
	    'PoSD\\D RSI[I RSM[M R_OlO R`VkV R`ZkZ RW[[[ RXDXQ R_G_O R`R`[ ReGeO R`RkRk[ R_GlGlO RaCbF RjCgG RYSZV RWTXW RaIbM RiIhM RUTUX RSTRZ RTDTQ\\R[[',
	    'Po^DlD RTD[D RTI[I RcKgK RTM[M R_QkQ R_TkT R]WlW RUTVX RXDXQ R_G_L R_N_T RcDcK ReTe[ RgDgK R_NkNkT R_GkGkL RYSZV RWTXW R[[X[ RSTRZ RTDTQ\\Q\\V[[',
	    'PoTE\\E R^HlH RTI\\I RTM\\M RaOiO RbXhX RX[[[ Rg[k[ RUTUX RXEXQ RTETQ\\Q\\Z R_Q_[ RaIaN RbTbY RbThThX R`LiKiO R_QkQk[ RgHlM RcH^M RYSZV RWTXW R^EdEaK RSTRY RiDaD',
	    'PoSD[D R]FdF RSH[H R\\JlJ RSM[M R]PdP R]SeS R]VdV RW[Z[ RWDWQ R]L]P RaCaJ R]LdLdP RlXl[ RiDkH RhWd[ RWSWW RkPfY RUTUX RkMhW RgCgPhZ RSTRY RSDSQ[Q[VZ[ RaSbY]Z',
	    'PoTD\\D RTI[I RaJhJ RTM[M RTQ\\Q R^RcR RfRjR RTDTQ RXDXQ R^M^R R^McMcR RfMfR RfMjMjR RhVl[ R`VcY RYSZV RdCkJ RcD]K RWTXX RhTeZ RaT^Z RUTUY RSTRZ R[[X[ R\\R[[',
	    'PoTEkE RTHjH RUMiM R[OaO R[RaR R[TaT RcThT RTWXW Ri[l[ RU[X[ R^[a[ RTETH RUJUM R\\X\\Z R^O^V RUJhJhM RcOiOj[ RlXl[ RTOXOYYZ[ R[O[VbVa[ RTOTVS[ ReOeUdZ RXSTS',
	    'PoSD\\D R^FlF RSI[I RbMhM RSM[M R_OkO RSQ\\Q RaTjT RaWjW R^ZlZ RSDSQ RXDXQ R_H_O RaRaW RbJbM RbJhJhM RaRjRjW R_HkHkO RdCfF RYSZV RWTWX RUTUX R[[W[ RSTRZ R\\S[[',
	    'PoRE_E RbFlF RUL[L RVNhN RVQgQ RVTgT Re[i[ RUIUL RZXZ[ RUI[I[L R\\C\\F R_N_V RdXeY R_X_Z RUXS[ RUFRI RcE_H RbFeJlM RiGfJ`M RVNVVjVi[ R^M[M RTH^H^M',
	    'PoTD\\D RTI\\I R_IkI R_LkL RTM\\M R^OlO R^TlT R_WkW RX[[[ RVTVX RXDXQ R_D_I RcEcI ReIeO ReTe[ RgDgI R_DkDkI RZS[V RXTXX RbQbT RhPhS RSTRZ RTDTQ]Q]V\\[',
	    'PoSD\\D R^HlH RSI[I RSM[M R^MkM R_QjQ R]TlT RXDXQ RcCcH ReMe[ RgCgH ReTkZ R_DaF RlDiG RhIgL RdT_Z RZSZV RWTXW R`IaL RUTUX R[[W[ RRTRY RSDSQ\\Q\\V[[',
	    'PoSD\\D R^EkE R_HjH RSI[I R`LiL RSM[M RaRiR R]UlU RUTUX RXDXQ RdCdH RdRd[ R`PiOiR R^J]KkJkM RcL^Q RaX]Z RdT_Y RZSZV RWTXX RdThYlZ R[[X[ RRTRY RbMaR RSDSQ\\Q\\V[[',
	    'PoSD\\D RaHiH RSI[I RSM[M RSQ\\Q R`RkR R`UkU R`XkX RW[[[ RSDSQ RUTUX RXDXQ RaOaW ReHeM R^OkOkW R^F^GlFlH RaJ_M RYSZV RWTXX RdCeF RhYl[ RcY_[ RjKfM^O RSTRZ R\\R[[',
	    'PoTD\\D R_GcG RTI[I R_JcJ RTM[M RTQ\\Q RTDTQ RUTVY RXDXQ R_D_M ReQe[ RgUlZ RfIiJlM RaQ^T RjJeN RaU]Y RjSgU RYSZV RWTXX ReQhW R^DfEkFhL RjPcQ RX[[[X[ RSTRZ RdDcN]N R\\R[[',
	    'PoSE[E R]FlF RSIZI RaIgI RSMZM RbSfS RWEWQ RSESQ[Q[Z RaCaI RbMbS RbWb[ RbMfMfS RgCgI RkMiO RYSXUXW RfWf[ RiWk[ R^W][ R]M`L`S\\S RUTUX R`M]M RZ[W[ RiKiTlT RSTRY',
	    'PoaDlD RSE^E RRH_H RTM]M RVQiQ RVShS RVUhU Rf[j[ RTITO R`Q`W RVEVH R_Y`Z RUYS[ RVQVWkWj[ RZYZ[ RdYfY RTKXKTK R]KYK RaCaFjGfH RaKdKaK RTI]I]O[O RdHgH ReHaIjJgKjK RgKdKgK RbHaNfNlNlO',
	    'PoSD[D RdEjE RSI[I RaJhJ RdMjM RSM[M RaQjQ RaTjT R_[l[ RW[Z[ RUTUX RXDXQ R`V`Z RaOaS RdCdG RdHdL RdVdZ RfOeT RgVgZ RaOjOjS R`VkVkZ R^HlGkJ RYSYV RWTWW RRTRY RSDSQ[Q[VZ[ R^G^T][',
	    'PoSD\\D R^GkG RSI[I R]KlK RSM[M R^NiN R_QiQ R_SiS RUTUY RXDXQ RaXaZ RdNdV RfWhY RfKkP RbJ\\P RYSZV RWTXX RdXdZ R_D`F RhDhF RSDSQ\\Q]X][ R[[X[ Rg[j[g[ RdD`M`VkVj[ RRTRY R\\S[[',
	    'PoSD[D R]ElE RSH[H RfLkL RSM[M R]NlN R^QkQ R]TlT RW[Z[ RUTUX RWDWQ RaVaZ R^H^KbLaT R^HcHcK RdCdF RfHgKhT RfHkHkK ReVj[ RWSWW ReT\\X RkVgW RSTRY RfZ^[ RSDSQ[Q[VZ[',
	    'PoTD\\D RTI\\I RTM\\M R_NkN RTQ\\Q R_QkQ R_TkT R^WlW RX[[[ RTDTQ RXDXQ R_K_Q RbQbW ReKeQ RhQhW R_KkKkQ RYSZV RWTXW RgYl[ RbY][ RUTUY RSTRY R^EcDcI]H RkEfEgJlIlJ R\\R[[',
	    'Po^DkD RSE[E RSIZI R_JcJ RfJjJ RSMZM R^McM RfMkM R]RcR ReSlS ReWlW ReZlZ RWEWQ RWTXW RSESQ[Q[Z R]H]J R`O`[ RdDdN RePe[ RePlPl[ R]HlHlJ R`ScW RYSYV R`Q]Y RUTUX RSTRY RZ[W[',
	    'PoSDYD R[FcF RSIYI RSMYM RdPjP R\\RbR R[VcV RRTRY RWDWQ RVTVW R\\M\\R R]C]J`J_[ RaCaJ R\\MbMbR RgIgY RgPiU RiJiN RfCcJ RY[V[ RTTUX ReKfNeV Rk[h[ ReGlGlVk[ RSDSQZQZVY[',
	    'PoTE[E R^GkG RTI[I R_LkL RTM[M R_OkO R_QlQ RbZhZ Ri[l[ RXEXQ RTETQ[Q[Z R^S^[ R^D^G R_J_R RbWb[ ReIeQ RbWhWhZ RkDkG R^SlSl[ RbU_V R`H]L RYSYUXW RfTjV R[[X[ RUTUX RSTRY RdCdGdI',
	    'PoTD\\D R^ElE RTI\\I RTM\\M R_McM RfMkM R_QlQ RTQ\\Q R`TkT R`WkW R`ZlZ RX[[[ RTDTQ RUTVY RXDXQ R_I_M R`P`[ RbCbG R_IcIcM RfPfZ RfIfM RhChG RfIkIkM RYSZV RWTXX RSTRZ R`P]S[[',
	    'PoSD[D R]DcD ReDlD RSH[H RSM[M R^MlM R^SkS R`VdV RXDXQ R^G^J R`H`J R`S`[ RcMcS R^GcGcJ RfGfJ RiHiJ R^PkPkS RfGlGlJ RgMg[lZl[ RWTXW RkVfW RUTUX RZ[W[ RSTRY ReZ_[ RSDSQ[Q[VZ[ R^M^U]Z',
	    'PoWDiD RWHhH ReNiN RTO^O RaOlO RTQ]Q RaQjQ RTS^S RaSkS Rh[k[ RVWVZ RYOYU R_D_J RdXcZ RfOfU RiWiY R_K_M RdKfL RVLSN RYXXY RZLZN RfXfY R`W`Z RWDWJjJiN RSWRZ R[W[Y RaOaVlVk[ RTOTV^V][Z[ RWFZFWF ReFhF R\\F`FcFeFcF R_F\\F',
	    'PoRUfU Rc[i[ RXIWPlPj[ RUEhEfP',
	    'PoV[[[ ReUl[ RfS_[ RbMgW RjKeU RaFeU R_EkEgR RZTRV RTE[F[JZP RUITP\\P[[',
	    'PoTD\\D R_KlK RV[[[ RbW^[ RfQjZ ReCdP`Y ReKhX ReLbW RZURV ReIgV ReJcU R[GZP RUITP]Q[[',
	    'PoSE[E RU[Z[ RfEfX RlCl[ RYURV R[GZP RTHSP\\QZ[ RaD`Q_Y',
	    'PoSEZE R`[k[ RT[Y[ RaFaZ ReCeU RlWk[ RkI\\M RYURV RkIkSgT RZGYO RTISQ[QY[',
	    'PoSE[E R_EkE R_ZlZ RU[Z[ R_E_Z RcLfO RaIeM RdSaW RhQjV RjHdT RYURV RZGYP RTISP\\PZ[',
	    'PoSE[E R`OlO R`YlY RV[[[ R`E`[ R`ElEl[ RZTRV R[GZP RTHTP]P[[',
	    'PoW[[[ RaNcSiYlZ R_FiJ RiDeI^L RiNfTaY][ RYURV RSE\\E[JZP RUHTQ\\Q[[',
	    'PoSD[D R`OlO RV[[[ R`HlHlQ RdDgF R[EZJYP RTITP\\Q[Z R`H`S_Z RYURV',
	    'Po`LjL R`SjS R][l[ RV[Z[ R`E`[ R`EjEj[ RYURV RSE[E[IYP RTHSP\\P[[',
	    'PoSE[E R^OkO RU[Z[ R^H^O R^HkHkO RcV^[ R_ReXl[ RYURV RZGYP RTHSP\\PZ[ ReCePcV',
	    'Po_YlY RV[Z[ R_D_[ R_DlDl[ RiDhSlS RYTRV RTHTP RSD[DZIZO RSQ\\Q[[ RdDdMbS',
	    'PoSDZD RbJlJ RV[Z[ Re[i[ R`G`[ RiCi[ RcNeT RaC\\N RYURV RTHSP RZEZJYP RSQ[Q[UZ[ R`D_L',
	    'PoSE[E RaUgU RU[Z[ Rf[j[ RaMaV RaMgMgT RbD^M RYURV R`HlHlTj[ RTHSP R[FYO RSQ\\QZ[',
	    'PoSE[E R_SlS R_ZlZ RU[Y[ R_MlMl[ RcF]K RbGjFgM RYURV R[FYP RTISQ[QZ[',
	    'Po]IkI R^QjQ R][l[ RU[Z[ RdIdZ RcCdG RYURW RSD[DZIYP RTISQ\\Q[[',
	    'Poa[k[ RV[Z[ RaKa[ RlWk[ R]HlHlK RcCdF RjN`S RYURV R]H]L RSE[EZKYP RTISQ\\Q[[',
	    'PoSWfW Re[i[ RbFfJlM RaEkEgJ`M RXDUH^K RWNgNfS RSE^FZKRM RXPXTjTi[',
	    'PobKlK RZK^K RSWgW Rf[j[ RbEbK RbElElK RXHSL RYDVJ RWNhNgT RSF_F^K RYOXTlTj[',
	    'Po^RjR R]VlV RU[Z[ RdOd[ R_FdKkO R]EkEeK]O RYURV RTHSO RSDZDZHYP RSQ[QZ[',
	    'PoSE[E RaMkM R^MiM R_ZkZ RV[Z[ R_Q_[ R_QkQk[ RaI]M RgHjJlN RdCaI RYURV RZGYP RTISP\\PZ[',
	    'PodGkG RaGhG R^HcH R]RkR Rf[l[ RT[Y[ RgRg[ RlXl[ RaW][ RkMkP RcJfNiO RhJ_N RaCcK RaR`Y RYURV RTISP RSEZEZJYP RSQ[QY[',
	    'PoSI]I RbIkI RRWfW Rd[j[ RSDSI RSD]D]I RbDbI RbDkDkI RULhLgS RYOWSlSk[',
	    'PoaLjL R_ZlZ RV[Z[ R_D_[ R_DlDl[ RfGeOjW RYURV RTHTP RSE[EZJYP RSQ\\Q[[ ReJdT',
	    'PoSEZE R]JlJ RU[Y[ RhQh[ RgKlP RbL^Q RdGaO R_Z][ RdEbM RYURV RZHYP RTISP[PZZ RbQaX RiD_E',
	    'Po\\TlT RU[Y[ R`F`P RdQd[ RfCfOkNlO RaC^J RjEfJaL RXURV RSEZEYJXP RTISPZQY[',
	    'PoSDZD R`FjF R_ZjZ RU[Z[ R_R_[ R_RjRj[ R`G\\J R`IeNlQ RaC_I RjGdM]P RYURV RZEZJYP RTJSQ\\QZ[',
	    'PoSD[D R]GlG R^OfO RV[Z[ RgVl[ RbK^N RdCeF RcHaM RhLaS]T RkPdW\\[ RYURV RTISP RZGYP RSQ\\Q[[',
	    'PoSE[E R_IkI R^QlQ RTHTP RhHh[ RaDbH RaW][ RiDgI RXURU RZ[V[ RZGYP RTQ[QZ[ RbIbRaW',
	    'PoTKdK RdNiN R`WhW RRXYX RX[\\[ Rf[j[ RSP[P[T RVDfDeH RXFXIjIiN RaPiPhT RbRaUlUk[ RTRTU^U][',
	    'PoSEZE R]ElE Ra[c[ Ri[k[ R^J^[ R^JcJc[ RfJf[ RfJkJk[ R_OaT RhPiT RXURV RU[Y[V[ RTHTP RYGXP RTQ[QY[',
	    'Po_JkJ R_NkN R^QlQ RV[Z[ Rd[i[ R_F_N ReCeN R_FkFkN RYURV RbQ`UjUi[ RTHTP RSE[EZJYP RTQ\\Q[[',
	    'PoaLhL R]ZkZ RU[Z[ ReFlM RdDfG RcG\\M RjPfZ RdCbH R_P`V RYURW RdOeU RSE[EZJYP RTISP\\PZ[',
	    'PoSE[E R^GkG R^NlN R^TlT RV[Z[ ReNe[ RdCeF R`HbL RiHfN RYURV RTISP RZGYP RSQ\\Q[[',
	    'PoSEZE R_HjH R^LjL RU[Z[ R]O]R R^DjDjK R]OlOlR RaTeXk[ R_RjSeX^[ RYURV RZGYP RTISP[QZ[',
	    'Po_IjI RT[Y[ RgKkO RfEkJ RcK^P RbP]U RdC_I RaSfXl[ RaRiRdX\\[ RYURV RTISP RSEZEZJYP RSQ[QY[',
	    'Po^HkH RaLiL RaPiP R^TlT RV[Z[ RaCaT RiCiT RgWk[ RbW^[ RYURV RSE[EZJYP RTHSP\\P[Z',
	    'Po^FkF R`WfW RU[Z[ Rf[j[ R`Q`X R`QfQfV R]NkNj[ RdIkL RdHbK^L ReCcJ RYURV RTHSP RSE[EZJYP RSQ\\QZ[',
	    'PoSD[D R^IkI R^NkN R^RlR RU[Z[ R^D^N ReDe[ R^DkDkN RfTlY RcS]Y RYURV R[EZJYP RTJSQ\\QZ[',
	    'PoSD[D R_IlI R_NkN R_SkS R_YlY RV[Z[ R`G`[ RfHfY ReCfG RaC^L RYURV R[EZJYP RTISP\\Q[[',
	    'PoSE[E R`LjL R^PlP Ra[e[ RV[Z[ R^G^J RePe[ RdCfG R^GlGlJ RiTkY R`T^Y RYURV RTISP RZGYP RSQ\\Q[[',
	    'PoSEZE R]JlJ R^NjN RdCd[ R^FjFjM RjPj[ RgRhX RaR`Y RXURV RX[U[ RYHXP RTISPZQY[ R^R]Z',
	    'PoSEZE RbHjH R_HgH R]LlL RfLkP RfEjI RcK^P RdC^I RdHbN RcQ_S RfS_W RiV_[ RXTRV RY[V[ RZGYO RTITP[QY[',
	    'PoSEZE R_KlK R`UlU RV[Y[ R_F_R`[ RdOdZ RhOhZ R_FlFlK RdCfF RXURU R`OlOl[j[ RTHTP RZGYP RTQ[QZ[ R^N]Z',
	    'PodFjF R[HlH RRXgX Rd[j[ RSDSO RdCdL R`K\\L RSEZEVHYJ RUQgQeU RYMUM R_E`I RWSWVkVj[ RhM\\O',
	    'Po`NiN R^SlS RU[Z[ RdSl[ RhJlN RlIhJ RcV^[ R^HaJ]N RjEfFiM R_EdE`L RYURV RTJSP RSEZEZJYP RSQ[QZ[ ReNdV',
	    'Po^UjU R^ZkZ R\\ZfZ RT[Y[ R^P^U RdMdZ R^PjPjU RhWl[ RbFdH R]EjEeJ\\N R^EbJgLlN RYTRU RSEZEZIXP RTHSP[PY[',
	    'PobFlF RSWfW Re[i[ RYHYM RWNXNgOfT RRH^H]K RcE`H RbFfKlM RVF]H RiGeK`M RTD^DYF RXHVKRL RXQWTjTi[',
	    'PoSE_E RTG^G RSJ_J RYP\\P RSXfX Rc[i[ RYCYJ RVM]M]P RWLRQ RcE`H RbGdKgNlP RVQfQdU RbGjGhJeM`O RYSXUjVi[',
	    'PoSEZE R^UkU R^ZkZ RV[Y[ R^E^N R^P^[ RePeZ R^PkPk[ RbHcK]M ReElElNhN RcD^E RXURV RgEfM RTHTP RZGYP RTQ[QZ[',
	    'PoUIiI RVKhK RRNlN RTXdX Rd[h[ RSFSH RWQeQdU RSEkEkI R^C`E RdGdNfQlS RZGZNXQRS RXSXUiUh[',
	    'Po^KlK Ri[l[ Rb[e[ RU[Y[ R`NeNe[ R^FlFlK RgNlNl[ RaQcS RhQjS RdCeF ReU_W RlUfW RYTRU RSEZEZIYP RTHSP[PZ[ R^F^R\\[',
	    'Po^ElE R^MkM R_PjP R^TlT RU[Z[ Ra[d[ R^H^M RcEcM ReTe[ RgEgM R^HkHkM RhVlZ R`W]Z RYURV RTISP RSEZEZJYP RSQ\\QZZ',
	    'PoSEZE R]HkH R]LkL RaTkT RT[Y[ Ra[d[ R]D]L RdDdL R]TfTd[ R]DkDkL RhWkZ RhRlU R`W\\Z RbL^PeP\\T RYURV RTHSP RZGYP RSQ[QZ[',
	    'Po^QkQ Ra[i[ RU[Y[ R^F^Q RbUbZ RiXi[ R^FkFkQ RdSgV RdLgO RcCcF RcH`K RiTlY R^U]Z RbIhJdN`O RYURV RSEZEYJYP RTISP[QY[',
	    'PoSEZE R^HkH RbKjK R`MkM RcQfQ RfWlW R`[b[ Rg[i[ RbUb[ RfLfP RiUi[ R^DkDkH RfSlSiU RXTRU R`SeScU^W RY[V[ RZFYKYP RTITQ[QY[ R^D^S\\[',
	    'PoSEZE R^GcG R^JcJ RU[Y[ R^E^M R^QdQd[ RfGlM RjIeN RaX\\Y RbV`X RdTgWlY RfElEiK RbS]U RkReU RXURU RTHTP RZGXP RTQ[QY[ R\\EdEcN\\M RiPaP',
	    'PoREYE R]NkN R]QkQ R]TkT R\\WlW R]K]Q RaQaW RdKdQ RgQgW R]KkKkQ RgYk[ RaY\\[ RfDfIkIlJ RkEfF RXURV R\\EbDaI[H RYGXP RTISPZPY[U[',
	    'Po]DkD R^JbJ RfJjJ R]MbM RfMkM R[RbR ReSkS ReWkW ReZkZ R\\H\\J R_O_[ RdDdN RePe[ RePkPk[ R\\HlHlJ R_SbW R_Q\\X RXURV RX[U[ RSEZEYIXP RTISPZQY[',
	    'Po]FlF RfKkK R]NkN R^QjQ R\\TlT RU[Y[ R`V`[ R^H^LbKaT R^HcHcL RfHfLgT RfHjHkL ReUjZ RdCdF RbU\\X RkVgW RXURV RfZ_[ RSEZEYJXP RTISP[PY[',
	    'Po^HhH RWSgS RWWgW Rb[g[ RWDWL RWOW[ RWOgOg[ RWDhDhL R^H^L RRLRP RRLlLlP',
	    'PoTS\\S RTV\\V Rh[l[ RY[\\[ RTOT[ RTD\\D\\L RTO\\O\\[ RTDSLRM^L^N R_JiJi[ RlWl[ RTHXHXL RcCcRbX',
	    'PoTS\\S RTV\\V Rh[l[ RX[\\[ RTOT[ RXHXL RTD\\D\\L RTO\\O\\[ RTDSL]L]O R^EhEi[ RlWl[ RcLfQ RRLRO R[GWH RaEaR`Y',
	    'PoWH[H Rh[l[ RWHWL RTO[O[[ RTD[D[L RRL\\L\\O R^IhIhZ RVRYS RbCaOfS RlWl[ R[UUW RW[[[W[ RTDSLSOTVS[ RaK_X',
	    'Po`EkE R_NlN RTS\\S RTV\\V Ra[e[ RY[\\[ RTOT[ RTO\\O\\[ RTD\\D\\L RTDSL^L]N ReEe[ RRLRN RXHWL R[GWH',
	    'Po`EkE R_NlN RTS\\S RTV\\V RTOT[ RTD\\D\\L RTDSL]L]N ReEe[ RRLRN RXHWL R[GWH RTO\\O\\[Y[',
	    'PoXH\\H RTS\\S RTV\\V RY[\\[ RTOT[ RTO\\O\\[ RTD\\D\\L RTDSL^L^N RfDiH RRLRN RXHWL RlVk[i[ RdCcKeW RgK`L RjKcL',
	    'Po_HlH RTS\\S RTV\\V Rh[l[ RY[\\[ RTOT[ RTO\\O\\[ RTD\\D\\L RTDSLRM^L^N ReCeH RaMiMi[ RlWl[ RXHWL R[GWH RaMaT`Y',
	    'PohLlL RTS\\S RTV\\V RX[\\[ RTOT[ RTD\\D\\L RTO\\O\\[ RTDSL]L]O RaDhDiL RlJlL RaPcUgYl[ R`PjOhTdX^[ RRLRO RXHWL R[GWH RaE`L',
	    'PoTS[S RTV[V RW[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O RhMh[ RfGlL RcH^M ReChI ReCbJ RRLRO RTHXHXL RbNbSaY',
	    'Po`RgR RTS[S RfUjU RTV[V Ra[k[ RY[[[ RRLRO RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O RaKa[ R`KgKgR RaHkGkT RlXk[ RcC_L RXHWL RXG[GWG',
	    'PoTO[O[[ RTD[D[L RRL]L]O RfCfN RdTjZ RVQXS RbNfW R`HkHjL R[UUW R`OkOhUdY`[ RWHWL R[GWH RX[[[X[ RTDSLSOTVS[ R`H`R^Z',
	    'PoTSZS RTVZV Rg[l[ RW[Z[ RRLSOT[ RTDZDZL RTOZOZ[ RTDSL\\L\\O R_J_Z RbDbY RgDg[ RlWl[ RjKgN RWHWL RWHZGWG ReMbM ReY][',
	    'Po]JkJ RTS[S R`VhV RTV[V RX[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O RdCd[ RiRlU R_R\\V RdHhPjT RdI_S RRLRO RWHWL R[GWH',
	    'PoWH[H R`MlM RTS[S RTV[V R^ZgZ RX[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O R`E`V RlVkZ RRLRO RWHWL RjD_F ReU]V RgDgNhX',
	    'Po_JlJ RTS\\S RTV\\V R`ZjZ RY[\\[ RRLRO RTOT[ RTD\\D\\L RTO\\O\\[ RTDSL^L^O R`Q`[ ReCeQ R`QjQj[ RXHWL R[GWH',
	    'PoWH[H R]HlH RTS[S RTV[V RX[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O RfVl[ R^LcQ ReH`M RiKbR]T RkQdX][ RRLRO RdDdI RWHXL',
	    'Po^GlG RTS[S RTV[V RX[[[ RRLRO RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O ReCeG RdTjZ RhJlN RfT`Z RbK^N RbOeV RhNeW RWHWL R[GWH',
	    'Po`JlJ RTS[S RTV[V RcYkY RX[[[ RRLRO RTOT[ RXHXL RTD[D[L RTO[O[[ RTDSL]L]O RcPc[ RcPkPk[ RTGXH R`E`S_[ RkD`E',
	    'Po^GlG RWH[H RRL]L RaMhM R^PkP RTS[S RTV[V Rd[h[ RX[[[ RTOT[ RTD[D[L RTO[O[[ RgIlM RbI^M RdC`L RTDSLRN RWHWL RbP`UiTh[',
	    'PoTS\\S RTV\\V RaZjZ RY[\\[ RRLRO RTOT[ RTO\\O\\[ RTD\\D\\L RTDSL^L^O RbQb[ R`SkRj[ ReLkS RhKaR RbGeN RbGjFfN RcC`J RXHXL R\\GXG',
	    'Po^QlQ RTS[S RTV[V RY[[[ RRLRO RTOT[ RTD[D[L RTO[O[[ RTDSL^L]O RdGeK RiFlK RaUl[ R`E_K RdM`V RXHWL R[GWG RiQgVdY][ RjD`E',
	    'Po^ElE R_MjM R_RjR RTS[S RTV[V RX[[[ RTOT[ RWHWL RTD[D[L RTO[O[[ RTDSL]L]O R_I_R R_IjIjR RcV_Z RaTdXj[ RRLRO R[GWH ReEeQcW',
	    'PoWH[H RfIlI R^QdQ ReQlQ RTR[R RTV[V RX[[[ RTOT[ RWHWL RTD[D[L RTO[O[[ RTDSL]L]O RiHi[ RjChJ RRLRN RcCaK R^IcIbR`X',
	    'Po_KkK R_OkO RTS[S R^UlU RTV[V RX[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O R_F_O RgQg[ R_FkFkO ReFeOaS ReCcF RRLRO RWHWL R[GWH',
	    'Po_IjI R_NjN R^RlR RTS[S RTV[V RTOT[ RTD[D[L RTDSL]L]O R_E_N ReEe[ R_EjEjN RfTlY RcT^Z RdQbV RRLRO RWHWL R[GWH RTO[O[[X[',
	    'PoTS[S RTV[V R`ZiZ RTDSLSOT[ RTD[D[L RRL]L]N R^F^I R`T`[ R`TiTi[ R^FlFlI RbJ_M RaKePlS RbKiKeP^S RTHXHXL RTO[O[[X[',
	    'PoaIkI RaMkM RTS[S RTV[V Ri[l[ RX[[[ RRLRO RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O R`P`[ RaDaM RfDfV RaDkDkM R`PlPl[ RXHXL R[GWH RhSjVaW',
	    'Po`GkG RcNjN RcRjR RTSZS RTVZV RgXjX Rc[l[ RW[Z[ RTOT[ RTDZDZL RTOZOZ[ RTDSL\\L\\O RdIdW RbKjKjW R]E_I R]P_P_X\\Z ReDbL RRLRO RWHVL RZHVH R_P\\P R^Yc[',
	    'Po]IlI R]SlS RTSZS RTVZV RW[Z[ RTDSLSOT[ RWHWL RTOZOZ[ RTDZDZL RRL\\L[N ReCeO R`DaG ReHiMlN RiDhH RdIaM]N RcPaU R_WlZ RiSgVeY][ RTGWHSH',
	    'Po^FlF RWH[H R`MkM R`PkP RTS[S R^TlT RTV[V Re[h[ RX[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O R`I`R ReCeQ RhRh[ R`IkIkR RaVdX RgDjD RRLRO RWHWL',
	    'Po]FlF R_MiM RTS[S RTV[V RaXhX Ri[l[ RTDSLSOT[ RWHWL RTD[D[L RRL\\L\\N R^P^[ R_I_M RaSaX RaShShX R_IiIiM R^PlPl[ RdCeF RTGXG RTO[O[[X[',
	    'Po_FlF RWH[H R^PlP RTS[S RTV[V RX[[[ RTOT[ RTD[D[L RTO[O[[ ReCeF ReMeP R_KlKlN RbGbJ RiGhJ RWHXL R`Z][ RTDSLRO Ri[e[ RRL]L]O RcTjTi[ RcPaX',
	    'PoaMlM RaQkQ RTS[S RaUkU RTV[V RaZlZ RX[[[ RRLSOT[ RTHXHXL RTD[D[L RTO[O[[ RTDSL]L]O R_F_J RbLb[ RgKgZ R_FkFkJ RbK^O RfCaN',
	    'Po_GkG R`KjK R^OlO RbSkS R`ZlZ RTD[D[L RTO[O[[ RTDSL]L]O ReGeO RfSfZ RVRYS RaCbF RbO^X RiChG R[UUW RRLRO RWHWL R[GWH RX[[[X[ RTOTVS[',
	    'Po_JkJ RTR[R RTV[V RX[[[ Rf[l[ RTDSLSOT[ RTHXHXL RTD[D[L RTO\\O\\[ RRL]L\\N R^F^H R`N`V RbHbL RfUf[ RgHgL R`NiNiV R^FlFlH RlWl[ ReCeF RdVaY][ RdQdW',
	    'PoaOkO RTS[S R^UlU RTV[V RTOT[ RTHXHXL RTD[D[L RTDSLRM]L\\N RbKbU RgOgU R_FlGlJ RiXl[ RbW^[ ReCfF RTO[O[[X[ R_G_J RiJaJ',
	    'Po]FlF RRL[L ReLkL RTSZS RTVZV RTDSLSOT[ RTGWHWL RTDZDZL RbHb[ RhHh[ RcDeF R_LcLdR RhKkW RbK^W RhKdW RTOZOZ[W[ RhJeV R]F]S[[',
	    'PoTSZS RTVZV RW[Z[ RRLSOT[ RTDZDZL RTOZOZ[ RTDSL\\L\\O R^EcEcL ReEkEkM RhFeI RaQ\\S RdM_R RjIfM RaF^I RbI^M ReMhQlR RdR_U RgT_X RWHWL RWHZHWH RiWdY^[',
	    'PoWH[H R]HlH R_KjK R_QjQ RTS[S R]TlT RTV[V RW[Z[ RTOT[ RTD[D[L RTO[O[[ RTDSL\\L\\O R_N_Q R_E_K RdCdQ R_EjEjK R_NjNjQ RRLRO RbQ_Wl[ RWHWL RiUeY][',
	    'Po_FjF R^JlJ R^McM RfMkM ReQlQ RTSZS R]TlT RTVZV Rg[l[ RW[Z[ RRLSOT[ RTDZDZL RTOZO[[ RTDSL\\L\\O RaKaQ RdCdJ RgTg[ RhKhQ RlXl[ RbT`Y\\[ RWHWL RZGWH RcQ]R',
	    'PoWH[H R^JlJ RTS[S R_UhU RTV[V R^YhY Rg[k[ RW[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL\\L\\O R^D^J RcOcX RgDgJ R^DlDlJ RfViY R_QhQhU RcDbJ^P RRLRO R_Q_U RWHWL RcH`MlMlVk[',
	    'Po^FlF RWH[H R^IlI R`LdL RTS[S R]UlU RTV[V RW[Z[ RTOT[ RTD[D[L RTO[O[[ RTDSL\\L\\O RbCbH RgSg[ RgCgH RgJgRkQlR RdN_R RaX^[ RaK]N R_NcO RkLfN RRLRO RWHWL RbSaY',
	    'PoaFlF RdHkH RcLlL RcQkQ RTSZS RcTkT RTVZV RgXjX Rb[l[ RW[Z[ RRLRO RTOT[ RTDZDZL RTOZOZ[ RTDSL\\L\\O RcOcX RgHgL RcOkOkX R]J_K R]E_G R\\P_P_X\\Z RdDaM RWHWL RZGWG',
	    'PoWH[H R_IkI R_MkM R]OlO RTS[S R_VjV RTV[V R][l[ RW[Z[ RTOT[ RTD[D[L RTO[O[[ RTDSL\\L\\O R_F_M R_R_V RcCcM RgCgM R_RjRjV R_FkFkM RbWb[ RgWg[ RRLRO RWHWL',
	    'Po`HiH R_RjR RTSZS R_UjU RTVZV RX[Z[ RRLSOT[ RTDZDZL RTOZO[[ RTDSL\\L\\O R^F^I ReCdF ReHeM R^OjOjW R^FkFkI RaJ^M R`O`WiXl[ RiK_O RbY][ RWHWL RZGWH',
	    'PoWH[H R`JjJ R`PjP R`RjR RTS[S R`UjU RTV[V RX[[[ RTOT[ RTD[D[L RTO[O[[ RTDSL]L]O R^F^H R`M`U RcHcK ReCeF RgHgK R`MjMjU R^FlFlI RgUg[lZl[ RhWjY RcUaY][ RRLRO RWHXL',
	    'PodEkE R`JhJ RdLjL RaQjQ RTSZS RaTjT RTVZV R^[l[ RW[Z[ RTOT[ RTDZDZL RTOZOZ[ RTDSL\\L\\O RaNaS RcVd[ ReCeL ReNeS RgVg[ RaNjNjS R^HlHlJ RRLRO R^H_W`[ R`VkVk[ RWHWL RZGWG R^Q][',
	    'PoRFlF RWMgM Re[k[ RTQT[ RWIWM R_C_F RWIgIgM RTQkQk[ RYTeTeXYXYT',
	    'PoRFlF RYJeJ RYMeM RZXdX Rf[j[ RTQT[ RYFYQ RZTZZ R_C_F RZTdTdX ReFeQ RTQjQj[',
	    'PoRF^F R`HkH RTM\\M RVW[W R[[^[ Rh[l[ RSPS[ RTHTM RVRVX RXCXF RVR[R[W RTH\\H\\M RSP^P^Z RbLhLiZ RlXl[ RdCeF RbLbUaZ',
	    'PoRF^F RaJkJ RTM\\M RVWZW Ri[l[ RSPS[ RTITM RVSVX RXCXF RVSZSZW RTI\\I\\M RbOiOj[ RaDkDkJ RlWl[ RSP^P^Z[Z ReLeQcY RaDaT_[',
	    'PoRF^F R`IkI R`MkM RTN\\N R_TlT RVXZX RSQS[ RTITM RVSVY RXCXF RVSZSZX RTI\\I\\M RSQ]Q][ R`D`M RcPcW RiOi[ R`DkDkM RaZ_[ ReO_Q RZ[][Z[',
	    'PoRG]G RTN[N Re[i[ RY[\\[ RSPS[ RTITM RVSVY RWCXG RTI[I[M RSP\\P\\[ R^N^Q R^NlNlQ RaHdK RhHlL RkEjH RcW`[ R^E^H RdEcH ReEeH R_K^L RVSZSZXUX RaC`K RdPcX RiCgK R_SjSi[',
	    'PoRG^G RaIiI RTN[N R_PcP RfPkP R^SlS RY[\\[ RSPS[ RTITM RVSVY RXCXG RTI[I[M RSP]P][ R_K_P RaDaI R_KcKcP ReQe[ RfKfP RaDiDiI RfKkKkP RfUlZ RcU^Z RVSZSZXUX',
	    'PoUD`D RUI_I RUM_M RRQaQ RiDfHbI RjKgOcQ RlTgXb[ RUDVQTZ R]XTZ R]T_V`YVY',
	    'PoUD_D RUG^G RUI^I RRK`K RSO_O Re[l[ RYQeQeZ R\\L_O RlWl[ RUDUKUO RiCbG RjGcJ RXWVYR[ RjLbO RYQXX',
	    'PoUD_D RUG^G RUI^I RRKaK RUO_O RSOZO RSRlR Rc[k[ RcRc[ R]M_O RlWl[ RUDVKUO RiCbG RkGcK RZVXYS[ R[RYX RlLgNbO',
	    'PoUD_D RUG^G RUI^I RRLaL RUO_O RSOZO RdXhX RX[k[ RXQX[ R_Q_X RhRhW R\\M_O RlXj[ RUDULUO RiCbG RkGcK RlKgNbO R]TSU RaSWU ReS[T RhR^T',
	    'PoUD_D RUG_G RUI_I RSKaK RRRlR Ra[g[ RUO_O`R R]L`O RUDVKVO R\\RZWXYS[ R[WhVg[ RiEcF RjHcJ RkMbO',
	    'PoUD_D RUG^G RUI^I RRL`L RUO_O RSOZO RRSlS R_P_[ RUDVLUO R`SdWlZ RiDbG RjGcJ R^SZWRZ RjMaO',
	    'PoUD_D RUG^G RUI^I RRKaK RRTlT RRWlW RUDUK RVQV[ RVLTO RiCbF RjHcJ RkLbN RSO[PhQh[d[',
	    'PoUD_D RUG^G RUI^I RRKaK RbTiT R^UeU R[UbU RVU^U RTU[U RcWjW R^XeX RZX`X RWX^X RRYYY R^[l[ RURYR^R_[ RSO^N_O RkXl[ RUDUKUO RiCbG RkGdJ RkMbO RfP]R',
	    'PoUD_D RUG^G RUI^I RRL`L RUO_O RSOZO R_[l[ RTRSU R`T`Z RSRkRkU RlXk[ RUDULUO RiCbG RjHbJ RkLaO R_P]VZYR[ R^R_X',
	    'PoUD_D RUG^G RUI^I RRK`K RUQjQ RRUlU Rc[k[ RUDUK RcUc[ RkXk[ RTO]N_O RXKTO RiDbF RiHbJ RjMaO R[UYYR[',
	    'PoUD_D RUF^F RUI^I RRKaK RSN_N RSPlP RVWjW Rc[h[ R_P_V RUDVKTN RVRUX RiDbF RjGbJ RkLbN R_SjSi[',
	    'PoUD_D RUF_F RUI_I RSK`K RRRlR RYUgU RSO]N`O RYTS[ RUDVKUO R[PXW RZT\\W`Yg[ RiDbF RjHcJ RkMbO R_Yk[ RfVaY]ZX[',
	    'PoUD_D RUG^G RUI^I RRKaK RSO_O RdTiT RVZhZ RWUW[ RSVhVh[ RSQiQiT R]L_O RUDUKUO RkGcJ RjDbF RlKgMbO R\\QZTRV',
	    'PoUD_D RUG^G RUI^I RRK`K RUO^O RSO[O RSQkQ RR[l[ R`S`Z RUDVKUO RiDbG RbTlW RjHbJ RkMaO RcQZVRX',
	    'PoUD_D RUG^G RUI^I RRKaK RTO_O RYT_T Rb[k[ RTST[ RYQYZ RbQb[ R\\M_O RlXk[ RUDVKUO RiCbG RjGcJ RjMbO RkSaV R]ZT[ R_YW[',
	    'PoUD_D RUG^G RUI^I RRKaK RSO_O RRSlS R]L_O RUDVKUO RdPiQ RiCbG RkGdJ R]P[TWYR[ R[S^VcYl[ RkLbO RfTdW`YX[',
	    'PoUD_D RUG^G RUI^I RRKaK RVN_N RSN\\N RVThT RRWlW Rc[h[ RUDUK RVQV[ R_O_W RVQhQh[ RVLTN RiDaF RjHcJ RkLbN',
	    'PoUD_D RUG^G RUI^I RRK`K RRSlS R^WgW RV[k[ RVPV[ R^P^W RgQgW RSOXO^N_O RUDUKUO RiCbF RjGcK RlKgNbO',
	    'PoUD_D RUG^G RUI^I RRKaK RSO_O RWWbW RW[k[ RWSW[ RlXk[ RUDUKUN RYNST RWSbSbW RiDaF RjHbJ RlKgMbO RhWeW RWRiQhW',
	    'PoUD_D RUG^G RUI^I RRKaK RUN^N RSN[N RUThT RUDUK R_O_T R\\K^N RUQjQhS RWLUN RZV_XgZ RiDbF RjHcJ RkKbN RfVaYZ[ R^Xj[ RcYV[ RURUY',
	    'PoUD_D RUF_F RUI_I RRKaK RSN_N RSUlU R\\[`[ R`U`[ R\\K_N RUDVKUN RiCbF RkGcJ RkVfX RkKbN RVQiP_S R`UYXRZ R[RbS',
	    'PoUD_D RUF^F RUI^I RRKaK RSN^N RUTiT RfZjZ RbNb[ RSPiPiS RUSTWjWjZ RUDVKTM RiDaF RjGcI RkKbM R\\N\\WYZT[',
	    'PoUD_D RUG^G RUI^I RRKaK RSQlQ Rf[j[ RUTU[ R\\T\\[ RcTc[ RUTjTj[ RSO^N_O R_Q_T RUDUKUO RiCbG RkGdJ RkLbN',
	    'PoUD_D RUF^F RUI^I RRK`K RTN^N RRPlP RWSfS RWVfV RTYfY RWPWY RgPg[ RUDVKTN RiDaF RjGbI RkKbN RjYfY',
	    'PoUD_D RUG^G RUI^I RRKaK RUO_O RSO\\O RUViV RUZiZ RURU[ R\\P\\Z RbPbZ RURiRi[ R\\L_O RUDUKUO RiCbF RkGdJ RkLbO',
	    'PoUD_D RUG^G RUI^I RRK`K RSO^O RTRjR RRVlV RUDUK RZPZV RdPdV R\\L_O RcXj[ RYKSN RiCbF RZXS[ RjHcJ RkLaN',
	    'PoUD_D RUG^G RUI^I RRKaK RSN_N RYRlR RWQW[ RbOb[ RUDVKUM RXQRV RbRgWlY RbR]WYY RiDaF RjHcJ RjLbN',
	    'PoUD_D RUG^G RUI^I RRKaK RSO_O RRSlS RVZhZ RUDUK RVVV[ RTQ`Q_V RVVhVh[ RVLTO RiDaF RjHbJ RkLbN RdP_P RhObP',
	    'PoUD_D RUG^G RUI^I RRKaK RSO_O RRRlR RTUjU RVZhZ RUDUK RVWV[ R_P_T RVWhWh[ RVLTO RiCbG RjHcJ RkLbO',
	    'PoUD_D RUF^F RUH^H RRJ`J RSM^M ReSlS RUUiU RVWiW RRZlZ RUDUJ R_T_[ RXJSM RjDaF RkFbI RZOSP RkJbL RWOfO`QXS RWP_RhS RcQUS',
	    'PoUD_D RUG^G RUI^I RRLaL RSO_O RVWhW RR[l[ R_P_[ RXR[U RfRhTjV RfQbU RUDULUO RXRSV RiDaF RjHcJ RkLbN',
	    'PoUD_D RUG^G RUI^I RRKaK RVUhU RVWhW Rd[h[ RVRV[ RSNZN_N_R RVRhRh[ R\\K_N RWOZP RfOcR RUDVKSN RiDaF RjGcI RkKbN',
	    'PoUD_D RUG^G RUI^I RRK`K RSN_N R_VcV RcOcV RhQlU RXVSZ R^QZU RUDVKUM RUPYQ RRSVT RiDaF RjHbJ RkKbN RgWbYY[',
	    'PoUD_D RUF^F RUI^I RRKaK RSN_N RUTiT RfZjZ R_Q_[ RUQiQiT RZN\\P RdNbP RUSUWkViZ RUDVKTM RiDbF RjGcI RkKbM R_VZYR[',
	    'PoUD_D RUF^F RUI^I RRKaK RSN_N RRS`S Rg[j[ RZPZ[ RcOcX RjOj[ RYSSY RUDVKUM RYU_X RiDbF RjHcJ RkKbN R^OTQ',
	    'PoUD_D RUG^G RUI^I RRKaK RSO_O RWQ^Q RWTkT RRWlW RUDUK RWQWW ReTfW RVLTO RiCbG RcXk[ RjHcJ RkLaN R[YS[ RfP\\Q',
	    'PoUD_D RUF^F RUI^I RRK`K RSN^N RWRgR RSUkU R[[_[ R_U_[ R`N`P RSPSQkPkR R\\K^N RUDVKTM RdWjZ RXXRZ RjDaF RjGbI RkJbM',
	    'PoUD_D RUF^F RUH^H RRKaK RWQhQ RWThT Rd[i[ RWOWT RSM[NhOhT R\\K_M RdV_[ RUDVKTM RZURX R]V[XV[ RWVjVi[ RiDaF RjGcI RkJbM',
	    'PoUD_D RUG^G RUI^I RRKaK RSN_N RY[][ Rf[j[ RUP]P][ RbPbX RbPjPj[ R\\K_N RUDVKTM RdRhS RWR[S RWU[W RdVhW RiDbF RjGcI RkKbN RUPUUTZ',
	    'PoUD_D RUF^F RUI^I RRKaK RSR]R RcZjZ Ra[h[ R^[f[ RXPX[ RTN^M_N RgWk[ RXS[W RbQgPlU R`R]U RUDVKUN RXSRY RcT`[ RiDaF RjHcI RjLbN',
	    'PoUD_D RUF^F RUH^H RRK`K RWM_M RTM\\M RRUlU RVXhX RZ[_[ R_R_[ RWRhRhX RUDVJTM R[OUR RYPdP^R RhDaF RjGbI RjKbM',
	    'PoUD_D RUG^G RUI^I RRK`K RRSlS R`YdY RY[g[ RYUY[ RYUdUdY RgXg[ RUDVKTN RYNUR RXPeOiP RaPdSlW RjGcJ RSN^NZTRW RhDaF RjKbM',
	    'PoUD_D RUF^F RUQiQ RcTkT RR[Y[ RUNUQ RbQbT RiNiQ RRJ]J_L RUDVJTL R]TUX RSM]MdP RXHUH RjDbF RkFcH RkIbL RZSSU R[H^H ReMYP R\\HYH\\H R]Yj[ RZVfV`YU[',
	    'PoUD_D RUF^F RRJ`J RTM_M RTOjO RUQiQ RRSlS RXXeX RX[e[ RYUY[ RWVfUe[ RUDVJUM RaQeUlX R]N\\RXURX RVHYHUH RiDaF RjFbI RkJbL R\\H^H R\\HZH RYH\\H',
	    'PoUD_D RUF^F RUI^I RRK`K RSN^N RRPlP RZ[][ RUSU[ RUS]S][ RbSbY RiRi[ ReNcP RUDVKTM RhDaF RWW[X RjGbI RjKbM Re[i[e[ RWU[U',
	    'PoUD_D RRJ`J RSLYMlN RZPhP RXRjR RWVhV RWXhX Rd[h[ RUDVJTL RWTW[ R`PaR RWThTh[ R\\LWQRS RXHUH RiCbF RkFbH RkIbL RUF]G^H R\\HYH\\H',
	    'PoUD_D RUF^F RUI^I RRKaK RSN_N RRQ_Q RaSjS RaWjW RTY]Y Rf[j[ RTUTZ RXOXU RTU]U]Y RaPjPj[ RaPaW][ RUDVKTN RiDbF RjGcI RkKbN',
	    'PoUD_D RUF^F RUI^I RRKaK RSN_N RRPaP RTU`U Rg[j[ RTRTU RZOZ[ RTR`R`U RdOdW RjNj[ R\\K_N RUDVKTM RXU`Y RZUWXRZ RjDaF RjGbI RjKbM',
	    'PoUD_D RUF^F RUH^H RRJ`J RSM^M RRS]S RUDUJ RYOY[ ReSfWjZ RXS\\W RjQhU RXJSM RYSRY R`Q_U RdVaY][ RiDaF RjGbI RkJaM RdPdW R\\NTO',
	    'PoUD_D RUH_H RSL_L RVNfN RRPlP R[R^SjT RVXhX RVZhZ RVVV[ RUDVIUK R^M^P RVVhVh[ RRI^I_K R[Q[T R\\F_F RjDaE RkFbH RjJaK RhM^PRS RUFXFUF RZF\\FYF RgQaR',
	    'PoUD_D RUF^F RRJ`J RSM^M RSOkO RRTlT RVVhV RUDUJ R\\N\\[ RcOc[ RVQhQhV ReMbP RXJSL RaVjZ R\\VSZ R\\H^H RXHUH RiDaE R[HXH R]HZH RjGbH RkJaL RYH\\H',
	    'PoUD_D RRJ`J RSL[McN RXOiO RUXlX RUQUX RYQhQhW R\\J^L RjDaE RjFbH RXHUH RUF]G^H RbWeW RcZk[ RkIbK R\\W_W\\W R[ZR[ RUEUL R\\HYH\\H R\\S`ScS R_WbW RfMaM RaS^S RhSdSaS RYS]SYRYV[VhVeWbW_W',
	    'PoUD_D RUF_F RUH_H RRK`K RSM]M RXRdR RXTiT RRXlX RSOSR RUDUJ RXRXW RcTcX RSOkOkR R[K^M R_MaO RXKSM RjDaF RkGbI RkJbM RfQbR RZZR[ RcZl[',
	    'PoUD_D RRJ`J RTM^M R^OhO R\\TiT R\\ViV R[XjX R[[l[ RcRcZ RUNWQ R\\J^M RUDVJTM R_NZP RSSWSWXRZ RXHUH RiD`E RkFaI RkIbK RUF]G^H RfPZS R\\HYH\\H R`QlR RVY\\Za[',
	    'PoUD_D RRJ`J RTL^L RSNkN RVShS RVUhU RRXlX RVQVU RdMcO RVQhQhU R\\J^L RXJSL RiDbF RjFcH RkIbK RUF]G^H R\\HZH RUDUIYH\\H RaZk[ R_X[ZR[',
	    'PoUD_D RUH^H RRJ`J RSL_L RcMhO RVOhO RUDVJUL RfPcR R[LZO R_PhU R_PUU RbSfRlU RiDaE RjFbH RVPSS[SRU R`SWV R\\F^F RjIaK RbVWX RUFXFUF RYF[FYF ReYV[',
	    'PoUD_D RUF^F RRJ`J RSL]L RSOkO RZQdQ Rf[j[ RTTT[ RZMZQ R_Q_[ RdMdQ RTTjTj[ RdUgY RYU\\Y RZWWY ReVbY RXJSL RjDbF RkFbH RkIbL R\\H^H RUDUIYH\\H R]H[HXHUH',
	    'PoUD_D RRJaJ RVRhR RTXhX RR[[[ Re[l[ RUDVJUL R\\J_M RUHYHUH RiCbF RkFcH RVNWQZPVP RkJbL RTUXU R]HZH R_P\\P RSM[MhNhQdP RcScV_U RTSTUjSjVfU RUF]G_H RYH\\H RcUfU R[S[V_U RYYg[ RYP\\P_PbPePbP_P R[UXU RfXX[',
	    'PoUD_D RRJ`J RVShS RWXgX RWZgZ RUDVJTL RVOVS RWUW[ R_P_S RWUgUg[ RUPiPhS RYQ[S RdQbS RSL]MdNkP RYNRQ RYHUH RiCbE RjFbH RkIbL RUF]G^H R]HYH\\H',
	    'PoUD_D RUF^F RUH^H RRK`K RSNXM]NkO R^SjS R^VjV R^XjX R^Q^X RcOcQ R^QjQjX RUDUKUM RYORR RZSSV RZWR[ RfZl[ RhDaF RjGbI RjKbM RaYZ[',
	    'PoUD_D RRJ`J RTSjS R][g[ RR[\\[ RTNTS RUTU[ R_T_[ RgTiZkZl[ RUDVJTL RaUeV RXU\\V RaXeX RXX\\X RSMYMjNjS RiDaF RjFbH RkIaL RXHUH RUF]G^H R\\HYH\\H R[OcPXQ',
	    'PoUD_D RRJ`J RSL_L RbQkQ R[XdX RaZdZ Rf[k[ RTMT[ RUDVJUL RTM]M]Q R[TdTdZ RbMkMk[ RhOkO RjDaE RkFbH RTQVP]PYQ R`UdU`U RUGWG]G_H ReOhO ReOaNbPeO R[U_U[UZY RXHUH R[HXH R]HZH RkIaK RYH\\H',
	    'PoUD_D RRJ`J RSL_L RTP]P RbPkP RTR]R RbRkR R[WdW R[ZdZ Rf[k[ RTNT[ R[U[Y RTN]N]R RbNbR R[UdUdY RbNkNk[ RUDVJUL RjDaE RUHWG]G_H RYHUH R^HZH RkFbH RkIaK RYH]H',
	    'PoUH^H RRJ`J RUOiO RUQ_RkS RWUiU RTXkX RUDUJ RUMUQ RTL\\L\\Q RcLcQ RVKYLjMjQ R\\J^L RhXdY RYWX[ RUD]E^F R^WcYk[ RTSTZ RiD`E RjFaH RjIbJ R_YWZ RXFUF R\\FYF\\F',
	    'PoUD_D RRJaJ RUPiP RRRlR RVVhV RZ[b[ RV[_[ RUNUP RYXY[ R\\N\\P RbNbP RSMZMiNiP RUDVJUL R^VcYk[ RiCbF RjFcH RkJbL RVTVUhTiWcY RUHWG]G^H R\\HYH\\H RaVRY',
	    'PoUD_D RUF^F RRJ`J RUUhU RURUU R_R_U RURhRhU RdSbU RUDUJVM RZS[U RSM^LfP R[PaOlQ RWWW[fZgY RYHUH RiDaF RjGbI R_MZORQ RjJbL R[H^H RZYWY[Y R]HZH RYH\\H RWWgWgZcY_YbY R\\Y_Y\\Y',
	    'PoUH^H RRJaJ R[QeQ RRVlV RTXjX R\\[_[ RSNSQ RVSVV R[O[Q R\\S\\V R`X_[ RbSbV RVShShV RTLXMkNkQ R_KaM RVDVJSL RWOUR RfPjQ R^OaO RjDaF RkFbH RkIbL RUFWE]E^F RXFUF RYF\\FYF',
	    'PoUD_D RRJ`J RSNSP RUDVJTL RSLYMkNkP RdWhW RiCaF RkFbI RkJbL R]PTQ RXHUH RUF]G^H RXSWW[VWXfYk[ R[ZS[ R\\HYH\\H R]U`VcV RVPgP^RVS R_W\\V RRShShWeVbV_V',
	    'PoUD_D RUF^F RRJ`J RSL^L RUV[V R_VeV RUX[X R_XeX RR[\\[ R][g[ RTPUTU[ RUDVJTL R_T_[ RTPjPjT Ri[lZl[ R]NaP RVNZP RdNjP RgLdM RTThTi[ RiCaF RjFbH RkJaL R[H^H R]HZH RUGYH\\H R[QdRYS',
	    'PoUG^G RRIaI RTK^K RRMlM RUQ]Q RTRkR RUTjT RRVlV RUDUI RYXX[ RZQZV RUNUO]N]Q RdPdV RXITK RiWdY RaPeP R^VdYk[ RiCaF RjFbH RkHbK RaNaOiNiPfP RaVRY R`ZX[ RXFUE^E_F RYF[F R\\FZF',
	    'PoUE]E RaEiE RUH\\H RaHiH RTL]L R`LiL Rf[l[ RRCR[ RYEYL ReEeL RlCl[',
	    'PoUE]E RaEiE RUH\\H RaHiH RTK]K R`KiK RcViV R`VgV RVW\\W Rh[l[ RRCR[ RYEYK RcMcZ ReEeK RlCl[ RZO`P RWR]S R^VXW RcU\\V',
	    'PoUE]E RaEiE RUH\\H RaHiH RTK]K R`KiK RVOhO RcXfX Rg[l[ RRCR[ RXRXX RYEYK R_M_Z ReEeK RXRfRfX RlCl[',
	    'PoUE]E RaEiE RUH\\H RaHiH RTK]K R`KiK RWPhP RVTiT Rh[l[ RRCR[ RYEYK R[M[T RcMcT ReEeK RlCl[ RcWfZ R\\VXZVZ',
	    'PoUE]E RaEiE RUH\\H RaHiH RTK]K R`KiK RXQ]Q R`QfQ RXTfT Rh[l[ RRCR[ RXNXT RYEYK ReEeK R`NfNfT RbTaZgYgZ RlCl[ R]MXN R\\TZYVZ',
	    'PoUE]E RaEiE RUH\\H RaHiH RTK]K R`KiK RVN_N R`PjP RWT^T RcZfZ Rh[l[ RRCR[ RWQWT RYEYK RWQ^Q^T ReEeK RfNfZ RlCl[ RbScV RXUXX R]U\\W R^XUZ',
	    'PoUE]E RaEiE RUG\\G RaGiG RTJ]J R`JiJ RUM_M RWR]R RWU]U Rh[l[ RRCR[ RWPWX RYEYJ RZMZO ReEeJ RlCl[ RcThY RfTaY RbPeW RbPhPdW RcL`S RUP_P]YWX',
	    'PoTE]E R`EjE RUH]H RaHiH RTK^K R`KjK RaRiR RURYR RUY\\Y Rh[l[ RRCR[ RUMVPYPXS RURUVYVXY RYEYK R[V[Y ReEeK RfRfZ RlCl[ RUM^M^P[P[R^R^V[V RhMaNaT`Y R^XXY',
	    'PoTE]E R`EjE RUH]H RaHiH RTK^K R`KjK RaRiR Rh[l[ RRCR[ RYMYS RYEYK R[V[Y ReEeK RfRfZ RlCl[ RVM^M^P[P R[M[S^S^V[V RhMaNaT`Y RVMVPYRUSVVYVZYUY R_YXY',
	    'PoUE]E RaEiE RUG\\G RaGiG RUI]I RaIjI RYLaL RUXhX RVZZZ R]ZhZ RRDR[ RXEYI RZQZT RWO]N^Z RaMaX ReEeI RVNdNfNfO RUSaRhRhW RcTeTbV RZVZZ R[IYLUM RlDl[h[ RWVZV RXQVQ',
	    'PoRPkP RT[k[ RRDRP RURU[ RkDkP RlXk[ RWEgM RUHYI R]MaN R\\D`E ReDaHUM RdHiI RZVTW RiSZV',
	    'PoSFZF RdFlF RUUaU RUW_W RSMSP RUPUU RUVU[ RWCWK RaPaU RhChK RSMkMkP RVFYI RgFkJ RWFRJ R\\IbK RXP]R R\\DbG RhDfI RaI[K R]O[QWS RiObQ RiScV RkUiXb[ RbD\\G R]ZU[',
	    'Po\\EbE RSFZF RdFlF RZHcH RUU`U RU[`[ RSMSP RUPUU RVVV[ RVCVK R_E_K R`P`U R[I\\KbJbK RgCgK RSMkMkP RUFYI RgFlJ RWFRJ R\\C[F RXP]R RgDeI R]O[QWS RhObQ RiSbV RkUhXb[ R^WUX',
	    'PoRElE RWLhL RbTiT RYVfV Re[k[ RSPS[ RWHWL R_V_[ RbPbT RWHhHhL RSPkPk[ R\\PZSUT',
	    'Po_EjE R\\JgJ RXJaKlL RWNkN RZRhR RdWkW R\\XfX Rg[k[ RWTW[ RZPZR R`C`K RaYa[ RcTdW RZPhPhR RWTkTk[ RUGkGjI R^VYW RUGUSS[',
	    'PoRE^E R_GlG RSM\\M R`PkP RYS]S R`UkU RTV[V R[[][ RROR[ RSHSL RXVXZ RYOYS RSH\\H\\L RRO]O][ R`K`[ ReCe[ RiCkF RVOUT R`KkKk[h[',
	    'PoTD^D RaDkD RRG^G R`GlG RYK^K RSMkM RWRhR RbWjW RYXeX Re[j[ RTTT[ RTDTH RWOWR R[G[K R_X_[ RaDaH RaTbW RWOhOgR RTTjTj[ RfDeHcJ_K RYDXHVJRK R[VVW RgHgKlJlK',
	    'PoSF]F RSI]I RfMlM RSOlO RWShS RWXhX Rg[k[ RTUT[ R_D_J R_Y_[ RfIfM RWQWRhQhS R_DjDjJ RTUkUk[ RXJ\\K RdGbK^M RXCXIVLRM',
	    'PoRE^E R`KjK RTM\\M RcSjS RYS^T RTV[V R[[][ RRPR[ RTHTM RXVXZ RTH\\H\\M RRP]P][ R`D`K RjDjK RgPkOkP RfGhI RdQ_V RhDbJ RbTfYl[ RbEfG RcKaP_P RiTeY_[ RYPYT RfLgP RWPUTRT',
	    'PoRE]E RTM[M RUU[U R`UkU R`ZkZ RZ[\\[ RSPS[ RTHTL RXUXZ RTH[H[L RSP]P][ R_E_N R`P`[ RePeZ R`PkPk[ RVQVS RZQXV RcHdL^M ReElElNhN ReD_E RhEfM',
	    'PoSF\\F RRI]I RfMlM RROlO RWShS RbWkW RYXfX Rf[k[ RSUS[ R_X_[ RbUcW RWQVRhQhS R_DjDjJ RSUkUk[ RXCXITM RfJfM RWJ[K RbJ`L]M RcJfJ ReJbJ R[WUX RjFgFdFfF R_FbF_E_JaIjIfJ',
	    'PoRE^E RaFlF RaKjK RTM\\M RaNjN R_QlQ RZT^T RUV[V R[[^[ RSPS[ RTHTL RXVXZ RTH\\H\\L RSP^P^[ RaIaN RaIjIjN RbR_V RdT_Y RgWc[ RhSeY RWPUT RbC_I RYPZT Rj[g[ RaTkTj[',
	    'PoSGYG ReGkG RZGdG RRMlM RVRhR RbWiW RYXfX Rf[k[ RSTS[ RVOVR RRDXDXF R_C_K R_X_[ RbTbW RVOhOhR RSTkTk[ ReDkDkF R_G[K R^HcJ ReFeIlIkKhK RRFRIYIXKUK R[VUW',
	    'PoUKiK RUPiP RcWjW Ra[l[ RUFUP R_F_P RbPb[ RUFiFiP RhTiX RlXl[ R^D\\F ReRcW R[PYWWYR[',
	    'PoSK_K RSP_P R[[k[ RSFSP RYFYP R[P[Z RSF_F_P RlWkZ RXCXF RiDfGbI RjJfNbO RkQgUbX R]Q\\V R_T`V\\V RWPUX',
	    'Po`GlG RSK]K RSP]P RZ[k[ RSFSP RZPZZ RSF]F]P RfCfL RbOjW RZV^U^W RWRS[ R\\Q[V RaLjLhQdU`W RXCXFXNVV',
	    'PoSK^K RbKlK RSP^P RZ[k[ RSFSP RZRZZ RSF^F^P RhKhX RkWkZ RWQSZ R\\R[W R^T^WZW RXCXFXMWU RkEbFbRaX',
	    'PoSK^K RSP^P RZ[k[ RSFSP RZRZZ RSF^F^P RhLhX ReElL RdG_L RkWkZ RWQSZ R\\R[W ReDcI R^T^WZW RXCXFXMWU RcLbRaW',
	    'PoSK_K RSP_P R[[k[ RSFSP RYFYP R[P[Z RSF_F_P RiDiW RcFfH RkWk[ RbKfM R\\Q\\W RVPUX RlP`R R_T`VZV',
	    'PoSF[F R]KkK RRL\\L R]PkP RfWlW R]F]P R]FkFkP RcCcF RYP\\V RjTkW RhRfW RULSU R[TSU RaP_W]YY[ RdFdXg[l[mY',
	    'Po`HlH RSK]K RSP]P RZ[k[ RSFSP RXEYPZZ RSF]F]P RfQkV RhDjF RgRbW RdCdKhT RiLeU R\\Q[W R^T_V[V RVPUX RdFbR',
	    'Po\\KkK RROYO R\\PkP RRVYV ReWlW RRHRX RRHYHYV R\\F\\P R\\FkFkP RdFePe[lZl[ RjTkW RcCcF RgRfW RUCUH R^V[YW[ R_P^W RcMdT',
	    'PoaHkH RSK]K R`MkM RSP]P RZ[k[ RSFSP RXEYPZZ RSF]F]P RfDfW RhPlT RfKhQ ReKbS R\\Q[W R_T_VZV RVPUX',
	    'PoaHlH RSK]K RSP]P RZ[k[ RSFSP RZPZZ RSF]F]P RaHaU RhDkF RhRbX RkLgT RXCXFXRTZ R\\RZW RdS`U R^T_WZW RkSkXiW RfCfMgV RXMVV',
	    'PoTElE RWIkI RYRiR RYUiU RdYiY Rb[k[ RYOYU RbVb[ RYOiOiU R`M^O RfVcY RcKkN ReGhH RaH]LWN R`U]XX[ RaO_W RTETSS[',
	    'PoSK]K RaNjN RSP]P RaRjR RgXjX RZ[k[ RSFSP RXEYPZZ RSF]F]P RaJaX ReCeJ RaJjJjX RhElI RbE`J R\\Q[W R^T_VZV RVPUX',
	    'Po_ElE RSK\\K RSP\\P RhWkW RYZkZ RSFSP RWFXRYZ RSF\\F\\P R`I`W RcJdQ R`IkIkW RWCWF RWRS[ R\\RZW R]T^WYW RgEgMiS RWLVW RcEcKbS RgIfS',
	    'Po_GkG RSK]K RSP]P R`PdP RY[l[ RSFSP RZSZZ RSF]F]P RZW]V]X R`K`P R`KdKdP RhDjF RgRbX RjKfT RjTjX RWRS[ RdS_S RgDgOhW RWCWFXNVV',
	    'PoaIkI RSK]K RaMjM RSP]P RaQjQ RaVkV RZ[k[ RSFSP RXEYPZZ RSF]F]P RbGbW RfGfV RgDfJ R\\Q[W R_T_V[V RbDaL RVPUX',
	    'Po`GkG RbJiJ RSK]K RbNiN RSP]P R_RlR RZ[k[ RSFSP RXEYPZZ RSF]F]P RbCbR RiCiR RhTkW RcT`X R\\Q[W R_T_V[V RVPUX',
	    'PoSK]K RbKiK RcOiO RSP]P RcThT RYV]V RZ[l[ RSFSP RXDXP RZPZZ RSF]F]P R`E`X RcOcS ReKeO RcGdJ RhGgK R]S^V R`EkEkWhW RVPUY',
	    'Po_ElE RSK]K RSP]P RZV^V RZ[l[ RSFSP RXDXP RZPZZ RSF]F]P R`I`W ReEeW R]S^V RiLjS RhLgR RcLdR R`IkIkWiW R[Q[U RVPUY',
	    'PoSH^H R`KjK R`PjP RRR^R RgVlV RYDYN R`G`P R`GjGjP RfGfZlZl[ R[K]M RXH[K RaW][ RYHRN RjTlW RUU]Y RhRgV RWOTV ReCdG RbT`Y R\\RZWXYS[ RbPaX R\\DSE',
	    'Po_GkG RSK\\K R^MlM RSP\\P R_QjQ RYZkZ RSFSP RYRYZ RSF\\F\\P RcGcX RgEgX R_JjJjQ RfRjV RbDbF RWRR[ R[RZW RbP`W R]T^WYW RXFWOTY RWJVV',
	    'Po_FlF RSK]K RaMjM RSP]P RbTiT RYV]V RhWkW RZ[l[ RSFSP RXDXP RZPZZ RSF]F]P R`P`X RaGaM ReCfF RjGjM R`PkPkW RhRiU RgGdJ RcHhJ R]S^V ReMcT RVPUY',
	    'Po_EkE RSK\\K R_LkL R`OjO RSP\\P R_RkR RbXeX RY[k[ RSFSP RYRYZ RSF\\F\\P R_H_L RcEcL ReReX RgEgL R_HkHkL RiTjW RaS`W RlXkZ RWRR[ R[QZW R]T^WZV RXCWFXMVV',
	    'Po_HjH RSK\\K R^NkN RSP\\P R_RjR R_UkU RY[k[ RSFSP RXFXRYZ RSF\\F\\P R_E`KaQ ReEeY R_KiKiQ R_EjEjK RWCVF RWRSZ R[RZW R]T^WZW RWLVW',
	    'PoUFlF RWI`I RbIkI RYRiR RYUiU RdYjY RYOYU R\\G\\M R`C`F RfUdY RfGfM RYOiOiU RfHkM RfHbM R\\HXL R\\I`L R]UZYV[ RUFUSSZ RaOaU Ra[k[lZ',
	    'PoSK\\K R^PlP RSP\\P RYV]V RY[l[ RSFSP RXDYPYZ RSF\\F\\P RaRcU RfGhJ R]T]V RjGgM RaD_GaH_M RhDgH RjReWaX RaP_X RbKcM^M RjKkMfM ReCdIfU RVPTY RkUkXiW',
	    'PoeEkE RSK]K ReNkN RSP]P RZV_V R`WlW RZ[l[ RSFSP RXEXP RZPZZ RSF]F]P ReIeM ReCeH ReOeW RhOhW RkPkU R`HlHlK RbPbTeTbT RhTkTgT RVPUY R`H_R^X RhKaK',
	    'PoSE^E RTH]H RaKjK RTM\\M RSO^O RaPjP RRR_R RYU_U RZ[\\[ RSTSZ RXCXH RXMXO R]S][ RaGaP RaGjGjP RfGf[lZl[ RYWZY RRJ^J^M ReCdF RhRgW RSTWTST RWTWYSY RkTkWgW RcPbX',
	    'PoTDlD RaJkJ RVWiW RcZiZ RhMlQ RiFjH RfIhO RWM_M`Va[kZl[ RcPaP ReWeY RWLWP[O R_Q\\Q RVSVW RbU_U RYUUU RXU[UXT RVSiSiVeUcT R\\WYZR[ RXF_F_I[H_I[IWGXJ[J R\\U_UcT RTDTKSP RfFeN',
	    'PoXEbE RdHlH RYIaI RYKaK RXMaM RUSiS RUUiU RUPUU RYGYK R]C]N RYGaGaK RUPiPiU RiHiO RSDWE RVJUMSO ReUbX RRGVI R`U`[kYk[ R_Q]VZYS[ RkDeEeHcO RhXbY',
	    'PoVOiO RVTiT RVIVT R[W\\[ R_J_T RUJiJiT RhVl[ RUXR[ R[CSL RYFcF_J RbWc[',
	    'PoTO^O RTT^T Re[j[ RTITS RYJYS RSK^J^S R[V[Z R]U^X RVCSK RVF[FYL RTVR[ RcR`Z RWVWZ R_FlFlSj[ ReFdLbV',
	    'PoTO^O RTT^T RTITS RZJZS RZV[Y RSK^J^S R^U^X RfOkZ RVCSK RVF[FYL RSVS[ RcQ_[ RWVWZ ReCeLhW ReLaX ReHfS RdHcU',
	    'PoUOaO RUTaT Rg[l[ RUIUS RYVY[ R[J[S RTJaJaS RfFfT RlDl[ RWGRK R]V]Z R`UaY RXCVI RTVR[ RWF^F\\J',
	    'PoTO]O RTT]T RTITS RYJYS RSK]J]S RZVZZ R]U^X RgRkZ RVCSL RVF[FYL RTVS[ RdQ_Z RWVWZ R`EeEfKaX ReGhV',
	    'Po_FkF RTO]O RTT]T R^ZlZ RSVS[ RTITS RWVWZ RYJYS RZVZZ RSK]J]S ReFeZ R]V]X RVCRL RUF[FXM',
	    'PoSO\\O RST\\T Rd[k[ RTITS RWVWZ RYJYS RZVZZ RSK\\K\\S RdFdZ RkWkZ R\\V]X RVCRL RUFZFXL RSVS[ RjE_G RlN^P',
	    'PoTO]O R`QhQ RTT]T Re[j[ RTITS RYJYS RSK^J^S RZV[Z R]U^Y RWCSK RVF[FYL RTVS[ RdC`L RWVWZ RbIlHlSj[',
	    'Po`NjN RTO]O RTT]T Ra[k[ RTITS RYJYS RZVZY RSK]J]S RaNaZ R`EjEjO RlWk[ R]V]X RVCSL RVF[FYL RWVWZ RSVS[',
	    'PoTO^O RTT^T Rb[e[ RTITS RYJYS RSK^J^S ReMe[ RdHgL R`EjEeJ RZV[Z R]V_Z RaHfJ R_MlMjR RVCSL RVF[FYL RTVS[ RWVWZ',
	    'PoSO\\O RST\\T Rf[l[ RSVR[ RTITS RXJXS RYVYY RSK\\J\\S RbGb[ RfDfZ RkIcQ R[U\\X RVDRK RUFYFXL RbD^N RVVVZ RbDaL RlWl[',
	    'PoaMkM RTO\\O RTT\\T RTITS RWVWZ RYJYS RSK]J]S ReUk[ RhUb[ R\\U]X RZVZY RcNfW RjNfW RVCRK RVF[FXL RTVS[ RaE`Q_Z R_EhEgM',
	    'PoTO\\O RbReR RTT\\T RTITS RWVWZ RYJYS RSK]J]S ReCeR R\\U]Y RYVZZ RiHlO RVCRK RVF[FXK RTVS[ RaH^O RjPhUdY_[',
	    'Po^HlH RTO\\O RTT\\T Ri[l[ RTITS RWVW[ RYIYS RSK\\J\\S RaLiLiZ RlWl[ RZVZZ R]V]Y RdCdF RVCRL RUF[FXK RTVS[ RaLaT`Y',
	    'PoTO]O R`SkS RTT]T Re[l[ RTITS RWVWZ RYJYS RSK]J]S R`K`U RfCfZ RkKkU RlXl[ R\\V]X RZVZZ RVCRK RVF[FXK RTVS[ RkF_G',
	    'PoTO[O RTT[T Re[i[ RTITS RWVWZ RXJXS RZVZZ RSK[J[S RdV_[ R\\V]X RgMbX RVCRL RUFZFXK RTVS[ RbD^M RdJ_T RhHeU R`IlIlPj[',
	    'PoTO]O RTT]T RTITS RWVWZ RYJYS RSJ]J]S RhMh[ RfFlK RcG^L RZVZY R]U^X RfCgH RVCSK RVF[FXL RTVS[ RdCbI RbMaT`Y',
	    'PoTO]O RTT]T Rf[l[ RTITS RWVWZ RYJYS RSK]J]S ReCeNfZ R_IlIlL RbW^[ RlWl[ RZVZZ R]V]Y RdQ`Y RVCRL RVF[FXK RTVS[ R_I_M ReIcV',
	    'Po`ElE R`LkL RTO\\O RTT\\T RTITS RWVWZ RYJYS RSJ\\J\\S ReUk[ RhTb[ R\\U]X RZVZZ RbNfW RjNfW RVCRK RVF[FXL RTVS[ RbLdU R`E`R^Z',
	    'Po`FjF R^MlM RTO\\O RTT\\T Rg[l[ RTITS RWVWZ RYJYS RSK\\J\\S RhMh[ RlWl[ R\\U]X RZVZY RVCSK RVF[FXL RTVS[ RcMaX',
	    'Po`MdM RTO\\O RTT\\T Rf[l[ RTITS RYJYS RSK\\J\\S R`D`Z RgDgZ RlWlZ R\\V]Y RYVZY RVCRL RUFZFXL RTVS[ RkKfM RWVWZ RdY]Z',
	    'PoVKhK RVNhN RWWgW RWZgZ RWGWN RWSW[ R_H_N RWSgSg[ RUIhHhN RaPcR RhPlS RUPRS RXFcE_I R[P[R RZCVHRI',
	    'PoSO\\O RSS\\S RTITS RVVWZ RYJYS RYVZY RSK\\J\\S ReTkZ RbY][ RfT_Z R\\U]X RaLfV RdDeH RiMdV RVCRL RUFZFXL RSVS[ RaIdS R^IkIfS',
	    'Po_EkE RTO\\O RTT\\T R`ThT R^ZlZ RTITT RWVWZ RYJYT RYVYZ RSK\\K\\T R\\V\\X RVCRL RUFZFXL RSVS[ RcE`T RaLiKgZ',
	    'PoTO]O RaQlQ RTT]T RTITS RWVWZ RYJYS RSK]J]S RaJlJlS R]V^Z RZVZZ RVCSK RVF[FXL RTVS[ RkDaFaS_Y',
	    'Po_ElE RTO]O RTT]T RhVkV RTITS RYJYS RSK]J]S R`J`V RfEf[ R`JlJlV RZVZZ R]U^Y RVCSK RVF[FYL RTVS[ RWVWZ',
	    'Po^HlH RTO]O RTS]S Re[i[ RTITS RZJZS RSK^J^S ReCeH RbW^[ RZVZZ R]V^X RVCSK RVF[FYK RTVS[ RWVWZ RcHcQbW RcNkNkVj[',
	    'PobEgE RTO\\O RTT\\T Rd[h[ RTITS RWVWZ RYJYS RSK\\J\\S RaW^[ R\\V]Y RYVZZ RhFkN RVCRK RVF[FXL RTVS[ RbG^N RdOaX R`OiOiVh[',
	    'PoTO]O RTT]T RTITS RWVWZ RYJYS RSK]J]S R`J`[ RZVZY R]V]X RgNjU RWCSK RVF[FXL RTWR[ R`JlJl[h[ RbDfDfIhR RfGdR',
	    'Po_KlK RTO]O RTT]T Re[l[ RTITS RWVWZ RYJYS RSK]J]S ReCeNf[ RlWl[ RgDkH RaW^[ RZVZZ R]V^X RVCSL RVF[FXK RTVS[ RdQ`Y ReIcU',
	    'PoaJlJ RTO]O RTS]S RTITS RYJYS RSK]J]S RgCgJ RaQiQi[ R\\U]X RZVZY RVCSK RVF[FXK RTVS[ RWVWZ RaDaR`Y',
	    'PoTO]O RTT]T Rd[k[ RTITS RYJYS RSK]J]S RdFdZ RlWl[ RZVZZ R\\V^Y RVCSK RVF[FXL RTVS[ RWVWZ RiD^G RkK_M RlQ^S',
	    'PoTO]O R`SfS RTT]T Re[j[ RTITS RYJYS RZVZY RSK]J]S R`K`U R`KfKfS R_EkEj[ R\\U]X RVCRL RUFZFXM RSVS[ RWVWZ',
	    'Po`LlL RTO]O R`RlR RTT]T RTITT RXVXZ RYJYT R[V[Z RSK]J]T R`E`T RfEf[ R`ElElT R^V^X RVCSL RVF[FXL RTVR[',
	    'Po_IkI RTO\\O R^PlP RTS\\S RTITS RWVWZ RXJXS RZVZY RSK\\J\\S R\\U]X RiUl[ RVCRL RUFZFXK RTVS[ ReCeP_Z RgY_Z RjXdY',
	    'Po^IlI RSO]O R`QiQ RST]T R`ZiZ RTITS RWVWZ RYJYS RYVZY RSK]J]S R`C`[ RiCi[ R\\V]X RVCRL RUFZFXM RSVR[',
	    'Po_ElE RTO\\O RTT\\T R_YlY RTITT RWVWZ RXJXT RZVZZ RSK]J]T ReIeW RgJlO ReJ^P R\\V]X RVCRL RUF[FYJ RgEdL RSVR[',
	    'PoaLiL RTO]O RTT]T RfYjY RTITS RWVWZ RYJYS RSJ]J]S RdPd[ R_PjPjX RfFlK RfCgG RcF^K R\\V]Y RZVZZ RVCSK RVF[FXL RTVS[ RdCbH',
	    'PoTO]O RTT]T Rb[k[ RTITS RWVWZ RYJYS RSK]J]S R`H`K RcLcZ RfCfH R`HlHlK RlWk[ RZVZZ R]U^Y RVCRL RVF[FXL RTVS[ RkNbR',
	    'Po^IlI RTO\\O RTT\\T RTITS RWVWZ RYJYS RZVZY RSK\\J\\S RdTjZ RfDjG R\\U\\X RbCbOeW RVCRL RUFZFXL RTVS[ R`S][ RaNjNgUcY_[ RbI_W',
	    'PoTO\\O RTT\\T R`YlY RTITS RWVWZ RYJYS RZVZY RSK\\J\\S R`D`[ R`DlDl[ R]U^X RfLjU RVCRL RVF[FXL RTVR[ RfGdS',
	    'Po_EkE RTO]O R_QlQ RTT]T RTITS RYJYS RSK]J]S ReEe[ RZVZZ R]V^Z RhHkN RVCRL RVF[FXL RTVS[ RbH_N RWVWZ',
	    'Po_MkM RTO\\O RTT\\T RTITS RYJYS RSK\\J\\S RgCg[ R^GkGkM R\\V]Y RYVZZ RVCRL RUF[FXK RTVS[ RWVWZ RkXhX R_M^SlRkX RcCcRbX',
	    'PoTO\\O RTT\\T Rc[i[ RTITS RWVWZ RYJYS RSK]J]S RcIc[ RjWi[ RbDfE RdS]Z R\\V]X RYVZY RjGdT RVCRL RVF[FXL RTVS[ RjLlT RjEgN R`M^T',
	    'PoYF^F RVSiS RVViV RTETK RWOWV RYCYK R`P`V RUQiPiV RaCaKkJkK RiXl[ RaXcZ RVXS[ RYNbM_P R[X\\[ RZLUQRQ RjEaG R\\JSK R_JWK',
	    'Po`HkH R_OlO RTO]O RTT]T RTITS RYJYS RSK]J]S ReCe[ RiUlY RZVZY R\\U]X ReMjW RVCSK RVF[FXL RTVS[ ReM`X RWVWZ',
	    'PoTO\\O RTT\\T RaTgT Rf[j[ RTITS RYJYS RZVZZ RSK\\J\\S RaMaV RaMgMhT R]V^X RVCRL RUF[FXL RTVS[ RWVWZ RbC`L RaHlHlTk[',
	    'PofNkN RTO]O RTT]T R`ZlZ RTITS RWVWZ RYJYS RSK]J]S R`Q`[ R`QlQl[ RdJ`N RZVZY R]U^X RVCSK RVF[FXL RTVS[ ReEcL R_ElEkN',
	    'PoTO^O RbQlQ RTT^T RbZlZ RTITS RWVWZ RZJZS RSK^J^S RbHb[ RbHlHl[ R[V[Z R]V_Y RVCSL RVF\\FYL RTVS[ RfCeH',
	    'PoTO]O R`QlQ RTT]T R`ZlZ RTITS RWVWZ RYJYS RSK]J]S R`I`[ RfCfZ R`IlIl[ RZVZZ R]V]Y RVCSL RVF[FXL RTWR[',
	    'Po^HlH RTO\\O RTT\\T RdYkY R_ZgZ RTITT RWVWZ RYJYT RZVZZ RSK\\J\\T RdCeG RfR_Y R]V]X RiUk[ RVCRL RUFZFXL RTVS[ RdHaO RjMgP_Q',
	    'PoTO]O RTT]T RTITS RWVWZ RYJYS RSK]J]S RfCfN RdTj[ R]U]X RZVZZ RbNfW R`HkHjL RVCRL RVF[FXL RTVS[ R`OkOhUdY`[ R`H`R^Z',
	    'PoeJlJ RTO]O RTT]T R`ZkZ RTITS RYJYS RSK]J]S R`Q`[ ReCeQ R`QkQk[ RZVZZ R\\V^Y RVCRL RVF[FXL RTVS[ RWVWZ',
	    'Po`KkK RTO]O R`RkR RTT]T R^ZlZ RTITS RWVWZ RYJYS RSK]J]S ReDeZ R\\V]Y RZVZZ RVCRL RVF[FXL RTVS[ RaF_N',
	    'PocMkM RaMiM R_MeM RTO]O RTT]T R`ZkZ RTITS RYJYS RSK]J]S R`Q`[ R`QkQk[ RZVZZ R]V^Y RiHlN RVCSK RVF[FXL RTVS[ RWVWZ RcCaM',
	    'PoTO\\O R`QgQ RTT\\T RgUjU Ra[k[ RTITS RYJYS RSK\\J\\S RaKa[ R`KgKgQ RaHkGkS RlWk[ RYVZZ RVCRL RUFZFXL RTVS[ RbC^L RWVWZ R\\V]Y',
	    'PocJlJ RTO\\O RTS\\S Re[i[ RTHTS RXJXS RYVYZ RSK\\J\\S RaGa[ RiCi[ R\\V\\Z RVCRL RUFZFXL RTVR[ RbD]O RVVWZ RdNeT RaE`L',
	    'PoaIlI ReNkN RTO^O RTT^T ReTlT RTITS RWVWZ RYJYS RSK^J^S ReHe[ RZVZZ R]V^Y RVCSL RVF[FYL RTVS[ RcD_M',
	    'Po_EkE RTO]O RaSjS RTT]T R_ZlZ RTITS RYJYS RSK]J]S RaEaZ RaKjKkS RZVZZ R]U^X RVCRL RVF[FXL RTVS[ RWVWZ',
	    'Po_JlJ RTO\\O RTT\\T R`ZkZ RTITS RWVW[ RYIYS RSK\\J\\S R`Q`[ ReCeQ R`QkQk[ R]V]Y RZVZZ RVCRL RUF[FXK RTVS[',
	    'Po_ElE RTO]O RTT]T RbYkY RTITS RYJYS RSK]J]S RbLb[ RaOkOk[ RZVZZ R]U^Y RVCRL RVF[FXL RTVS[ RcI_S RWVWZ RdEaQ',
	    'PoTO\\O RTS\\S RTITS RWVWZ RYJYS RSK\\J\\S R\\U^Y RZVZZ RbGcKhPlR RcRhT RVCRK RVF[FXK RTVS[ RbC_J RaGiGgKbP^R R`WiZ',
	    'PoTO[O RTS[S RTITS RWVWZ RXJXS RYVYY RSK\\J\\S RaHa[ R\\U\\X RhEjH RVCRL RUFZFXK RTVS[ RbD^N RlXl[iZ RfCfNhY RkKbL',
	    'PoTO[O RTT[T R`[d[ Rh[l[ RTITS RWVWZ RXJXS RYVYY RSK[J[S R^EdEd[ RfElEl[ RdO_T RlOfT R[V\\X R_I`N RgIhN RVCRK RUFZFWM RTVS[',
	    'Po^HlH R_NkN RTO]O RTT]T R`ZjZ RTITS RWVWZ RYJYS RSK]J]S R`R`[ ReCeN R`RjRj[ R\\U]X RZVZY RVCSK RVF[FXL RTVS[',
	    'PoeHkH R^MlM RTO]O RTT]T Ra[e[ RTITS RWVWZ RYJYS RZVZZ RSK]K]S ReCeMe[ R]V]X RiPlW RVCRL RVF[FXM RSVR[ RaQ_W ReJeQ',
	    'Po`JlJ RTO\\O RTT\\T RbYkY RTITS RWVWZ RYJYS RYVZZ RSK\\J\\S RbPb[ RbPkPk[ R\\U]X RVCRL RUFZFXK RSVS[ R`E`T^[ RjD_E',
	    'PoXGfG RRJlJ RWShS RWVhV RXPXV R`P`V RVQhQhV RhXk[ RaXcZ RfDiH RVXS[ RZNbN_Q RYDTH R[X\\[ RaGfLlN R^C\\HXLRO R\\MTQ',
	    'Po^ElE RTO\\O RTT\\T RTITS RYJYS RSK\\J\\S R_K_[ RdJdZ RgKgZ R\\V^Y RYVZZ RVCRL RUF[FXL RTVS[ RWVWZ R_KkKk[i[ RdEcO',
	    'Po^NlN RTO]O RTS]S RTITS RYJYS RSJ]J]S R_GlGlK R\\V]Y RZVZZ R`TiX RjZl[ RVCRK RVF[FXK RTVS[ RgUdY^[ RWVWZ ReCeG RiNfW RdI`U R_G_K',
	    'Po_KlK RTO\\O RTT\\T RgUjU Rc[l[ RTITS RYJYS RSK\\J\\S RdNd[ RcNjNjT RlXl[ R\\V]Y RYVZZ RVCRL RUFZFXL RTVS[ RbFhFfJ RWVWZ RcC`I_J`T_Z',
	    'PoTO[O RTT[T Rg[l[ RTITS RWVWZ RXJXS RYVZZ RSK[J\\S RgDg[ RlWl[ RgPkT RcP_S RaW][ R\\V\\X R_HaM RkGhM RVCRL RUFZFXL RTVS[ RcDcQbW',
	    'Po`IkI RTO\\O R_OlO RTT\\T RTITS RWVWZ RYJYS RZVZZ RSK\\J\\S ReCe[ RhUlX RbT^Y R\\U\\X ReMiV RVCRL RUFZFXL RTVS[ ReNaV RaE_L',
	    'Po_MdM RTO\\O RTS\\S RhWlW RTITS RWVWZ RYIYS RYVYY RSJ\\J\\S R_F_W RfEf[ RfElElV R\\U\\X RVCSL RVFZFXL RdD_F RTVS[ RdU^W',
	    'PoSDbD RgOlO RXPeP RUTjT R`R`W ReEeL RSRjRjW RlClN RVQVWjXk[ RbYd[ R\\Y\\[ RWH`G\\KUN RVYR[ RZDWHRI RWJ[LRO R[OSQ',
	    'PoTO\\O RTT\\T RaZjZ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S RaRa[ RaRjRj[ RhJaR R]U]X RbGdLhPlR RVCRL RUFZFXL RTVS[ RcC`K RbGiFfM',
	    'PobIjI RTO\\O RTT\\T RcTiT Rh[l[ RTITS RYJYS RSK]J]S R_E_[ RcMcV RcMiMiT R_ElEl[ R\\V]Y RZVZZ RVCSL RUF[FXL RTVS[ RWVWZ',
	    'Po^GlG R_OkO RTO]O RTT]T RTITS RWVWZ RYJYS R_JkJkN RfVk[ RcV^Z R\\V]X RZVZY RdCdRhY RVF[FXL RTVS[ RkWiW RVCSK]J^Q^SlRkW RdNcV',
	    'Po`MlM RTO]O RTT]T RaUkU RaZkZ RTITS RYJYS RSK]J]S RaCaL RaPa[ RaPkPk[ RlJkM RZVZZ R\\V^Y RVCRL RUF[FXL RTWR[ RWVWZ RjE`G',
	    'Po_IlI RTO]O R^PlP RTT]T RTITS RYJYS RSK]J]S RhIh[ R\\V]X RZVZZ RaCbH RVCRK RVF[FXK RTVS[ RjChH RWVWZ RbIbSaX',
	    'Po^GlG RTO]O RaQjQ RTT]T RaUjU Rg[j[ RTITS RWVWZ RYJYS RSK]J]S RbJb[ RaMjLj[ R\\V]Y RZVZZ RVCSL RUF[FXK RTVS[ RcF^P RcC`N',
	    'Po^GlG RTO\\O RTT\\T RTITS RWVWZ RYJYS RSJ]J]S ReCeG RhJlN RaK^N R\\U]Y RZVZZ RaOdUhYl[ RVCSK RVF[FXK RTVS[ RhNfUaY][',
	    'Po^GlG RaMhM RTO\\O R^PkP RTT\\T Rc[g[ RTITS RWVWZ RYJYS RSK\\J\\S RgIlN RgGhL RbJ]N R\\U]Y RYVZZ RVCRK RVF[FXL RTVS[ RdC`L R`UiUh[ RaPaV',
	    'Po`GkG R_NlN RTO]O RTT]T R_TkT R^ZlZ RTJTS RYJYS RTJ]J]S ReCeM ReOeZ RZVZY R]U]X RVCSJ RVF[FYL RTVS[ RWVWZ',
	    'Po_HkH R`NkN RTO]O RTT]T R_TlT RTITS RWVWZ RYJYS RSK^J^S ReHe[ RZVZZ R]U_Z RaCbG RVCRL RVF[FYK RTVS[ RiChG',
	    'Po`LiL RSO\\O RSS\\S R_ZjZ RTITS RYJYS RYVYY RSK\\J\\S R_Q_[ R_QjQj[ RfGlM RcG\\M R\\U\\X ReCgH RVCRL RUFZFXL RdCbI RVVWZ RSVR[',
	    'PoTO]O RTT]T RdThT R`YlY RTITS RYJYS RSJ]J]S R`E`[ RdJdT RdJhJhT R`ElEl[ RZVZZ R\\V]Y RVCSL RVF[FXL RTVS[ RWVWZ',
	    'Po_GhG R^MlM RTO\\O RTT\\T Rb[k[ RTITS RYJYS RSJ\\J\\S RbPb[ RcCcM RhJ^T RlXk[ R\\V]Y RYVZZ RjDhJ RVCRL RUF[FXL RTVS[ RjRbU RWVWZ',
	    'PoaKlK RTO\\O RTT\\T RTITS RXIXS RYVYZ RSK\\J\\S R`G`[ R[V]Y RhSk[ RiEkI RVCRK RUFZFXL RTVS[ RaD\\O RVVWZ RfCfOjY R`E`L RfLdX RfIhW',
	    'Po_GiG R^LlL RTO\\O RbPkP RTT\\T Re[i[ RTITS RWVWZ RYJYS RZVZY RdCdL R]U]X RUFZFXL RTVS[ RkEjIbP]S RcObUjUi[ RVCSK\\J]SXS',
	    'PobJlJ RTO]O RTT\\T RTITS RXIXS RYVYZ RSK\\J\\S R`G`[ RgCg[ R[V\\Z RVCRK RUFZFXL RTVS[ RVVWZ RgHjU RaC^M RfHcU',
	    'PoTO\\O R_QjQ RTS\\S Rg[l[ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S R_I_P RhQh[ R_IkIkP RlXl[ R]U]X RaCcG RVCRL RVF[FXL RTVS[ RiCgI RaZ^[ RcQbY',
	    'Po_IlI R_NjN RTO\\O RTT\\T RaZjZ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S RbQb[ R`DjDjN RaSkSk[ R]U]X RVCRL RUFZFXL RTVS[ ReEaT',
	    'Po`MkM RTO]O RTT]T RTITS RWVWZ RYJYS RSK]J]S ReHe[ R_HkHkM RaCbG RiChH RdU^Z RZVZY R]V]X RePcV RVCSK RVF[FXL RTWR[ RkYhY R`M_SlRkY',
	    'PoTO]O R`RkR RTT]T Rf[l[ RTITS RWVWZ RYJYS RSK]J]S RaIaR RgRg[ R_KkKlR RlWl[ RcV_[ R\\U]X RYVZY RVCRK RVF[FXL RTVS[ RcC_K RbFhFeKdV',
	    'PoTO\\O R`PjP RTS\\S R`UjU Rf[j[ RTITS RWVWZ RYJYS RSJ\\J\\S R`K`[ ReCeK R`KjKj[ RjFlI R\\V^Z RYVZZ RVCRK RUF[FXK RTVS[ RaE^J',
	    'PoTFjF RVIiI RRLlL RZOdO RWThT RWWhW RXQXW R_Q_W RURhQhW RiXk[ RbYd[ RYC[F RVXS[ ReCcF R\\Y\\[ R_GZLRP R\\NTQ',
	    'Po^IkI RTO\\O RTT\\T RTITS RYJYS RSJ\\J\\S RgIgNkMkN RhFkJ RbQ^U R\\U]Y RYVZZ RaSfYl[ RVCRK RUFZFXL RTVS[ RcCaI`N RcOaSiSeX^[ RWVWZ',
	    'Po^HlH R`KiK RTO\\O R_RjR RTT\\T RTITT RWVWZ RYJYT RSK\\J\\T R`EiEiK R^N^PkNkQ R\\U]Y RYVZZ RaTeXl[ RVCRL RVFZFXL RTVS[ RiSeX^[',
	    'PoaLiL RTO\\O R_QlQ RTT\\T Rb[e[ RTITT RWVWZ RXJXT RZVZZ RSK\\J\\T ReLe[ ReDlK RdE^K R\\V]X RiTkY RVCRL RUFZFXL RTVS[ RaT_Y',
	    'PoTO\\O R^QlQ RTS\\S RTITS RWVWZ RYJYS RSJ\\J\\S RdGeK R\\U]Y RZVZZ RiFlL R`Ul[ RVCRK RUF[FXL RTVS[ RdM_V RiQgVdY^[ R`E^L RjD`E',
	    'Po\\EkE R_KdK RTO[O R_QdQ RTT[T Rg[l[ RTITS RVVVZ RXJXS RXVYZ RSK[J[S R_E_W RdEd[ RhFhZ R[V[Y RlWl[ RVCRK RUFYFWL RTVR[ RdV]W',
	    'PoTO\\O R^QkQ RTT\\T RTITS RYJYS RSK\\J\\S ReQe[ R_JcN RhNlR RhIeK`Q R\\U]X RYVZY RiTkY RVCRL RUFZFXL RTVS[ RcEaJ RaT^Z RWVWZ RjC^E',
	    'Po^ElE R_MkM RTO\\O R_RkR RTT\\T RTITS RWVWZ RYJYS RSK]J]S R_I_R R_IkIkR R\\U]X RZVZY RVCSK RVF[FXL RTVS[ RdVaY][ R`TdXgZl[ ReEeQdV',
	    'Po^ElE RSO\\O RgQkQ RST\\T R_VkV R_ZkZ RTITS RXJXS RYVYY RSK\\J\\S R_J_[ RcEcO RhEhP R_JkJl[ R\\U\\X RVCRL RUFZFXL RSVR[ RVVVZ',
	    'Po_DkD RSO]O R_OlO RST]T R`ZkZ RTITS RWVWZ RYJYS RYVZY RSK]J]S R`R`[ R`IiIiO R`RkRk[ R\\V]X RVCRL RUFZFXM RSVR[ RdEbO',
	    'Po^HkH RSO\\O RST\\T Ra[d[ RTITS RWVWZ RYJYS RYVYZ RSK\\J\\S RdCd[ R^LaN RgSlX RhDjF RdQ^W RkLfQ R\\U\\X RdLiV RVCRL RUFZFXL RSVS[ RdHfR',
	    'PogMkM RTO[O RTT[T RdVlV Rc[l[ RTHTS RVVVZ RXJXS RYVYY RSK[J[S RdKdV RhEhV R_VbZ R[V[X R]R`Y RUDRK RUFYFWL RSVS[ RkDcF R\\EaE]MaM_X',
	    'Po^GlG RTO\\O R_PkP RTT\\T R_UkU RTITS RYJYS RSK\\J\\S R_K_[ ReCe[ RhDlE R\\V]Y RYVZZ RVCSK RVF[FXL RTVS[ RWVWZ R_KkKk[h[',
	    'PoaLjL RTO\\O R_QlQ RTT\\T Rg[l[ RTITS RWVWZ RYJYS RSK]J]S ReCeG RhQh[ R^GlGlK RlXk[ R\\V]X RYVZY RVCRL RVF[FXL RTVS[ R_[][ R^G^K RbQaY',
	    'Po_GkG R^TjT RWHW[ RZGZW RePeW R]QjQjW R_P_WjXl[ RaM]Q R`NfNcP R^Y\\[ RaD\\I RbYb[ ReYf[ RXDTM R_FbJfKlM RhFeJaL[M',
	    'Po_JkJ RTO]O R_PkP RTT]T R_TkT R][l[ RTITS RWVWZ RYJYS RSK]J]S R_E_P ReEeZ R_EkEkP R\\V]X RZVZZ RVCRL RVF[FXL RTVS[',
	    'Po_IcI RWTiT RWWiW RXPXV R`Q`V RcCcI RURiQiV RaYc[ RhDkH RYOcO`R RWISM R^D[H R[Y\\[ RUDXD RRGVG RgYl[ R[OSR RVYR[ ReKXM',
	    'PoTO[O RTT[T RTITS RVVVZ RXJXS RYVYY RSK[J[S RgCgS R\\K`M R^DbE R[V[X RiHlN RUDRK RUFYFWL R_S\\Z RjPhUeY_[ RSVS[ RcHaP',
	    'Po`IkI R^NlN RSO\\O RST\\T R`ZkZ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S R`R`[ ReCeN R`RkRk[ R\\U]X RVCRL RUF[FXL RSVS[ RaD_K',
	    'Po^GlG R_MkM RTO\\O RTT\\T Ri[l[ RTITS RWVWZ RYJYS RYVZZ RSK\\J\\S R\\U\\X RdPdZ RiPi[ RgJkN RcCdGbK^N RVCRL RUFZFXL RTVS[ RlXl[ R`P`U_Z',
	    'Po_IlI RTO\\O RTT\\T RTITS RWVWZ RYJYS RSK\\J\\S RhDjF RgWc[ R\\U\\X RYVZZ RkNfX RVCRL RUFZFXL RTVR[ Ri[k[ RlWk[ R_NdNcV`V RgDgMhZ R_I_S][',
	    'Po^KeK RTO[O RTT[T Rh[l[ RTITS RVVVZ RXJXS RYVYZ RSK\\J\\S RbEb[ RgGgV RlCl[ RbNeR R[V\\Z RVCRK RUFZFXL RbL]U RTVR[ ReD]E RaHaP',
	    'Po_HjH R^MlM RTO\\O ReSkS RTT\\T Re[l[ RTITS RWVWZ RYJYS RSK]J]S ReCeZ R\\V]X RYVZY R`ScY RVCSK RVF[FXL RTVS[ R`P^Y',
	    'Po^MlM RTO\\O RTT\\T RTITS RYJYS RSJ\\J\\S RaPaY RfNf[ RYVZZ RaEkI RVCRL RUF[FXL RTVS[ RiE^J RWVWZ R\\V]Y R_DiG RdI`RkQkXhX',
	    'PoTO\\O R`PlP RTT\\T R`UlU Ri[l[ RTITS RYJYS RSK\\J\\S R`K`[ RfKf[ R`KlKl[ R\\V^Z RZVZZ RbGjK RVCRL RVF[FXK RTVS[ R_ElEiGeH RWVWZ',
	    'Po`JiJ R`NiN RTO\\O R^RjR RTT\\T Re[i[ RTITT RWVWZ RYIYT RSK\\J\\T R`F`R R`FiFi[ RdCcF R\\V]X RZVZZ RVCRL RUFZFXL RTVS[ RlOfV]Z',
	    'PoTO\\O RTT\\T R`TkT R`ZkZ RTITS RYJYS RSJ\\J\\S R`O`[ ReOeZ R`OkOl[ RhHkK RjDiH RZVZZ R_IaM RdIfM RVCRL RUF[FXL RTVS[ RaC^H RfCcH RWVWZ R\\V]Y',
	    'Po^HlH RaLiL RSO]O RaPiP RST]T R^TlT RTITS RWVWZ RYJYS RYVZY RSK]J\\S RaCaT RiCiT RhWl[ RbW^[ R\\U]X RVCRL RUFZFXL RSVR[',
	    'Po_FkF R`IkI R^LlL RTO]O RTT]T R`TjT Rg[j[ RTITS RWVWZ RYJYS RSK]J]S R`O`[ ReCeL ReOeT R`OjOj[ R\\V]Y RZVZZ RVCRL RVF[FXL RTVS[',
	    'PoTFjF RVIhI RRLlL RXTgT RXWgW RXQXW R_R_W R_F_L RWRgRgW RhXj[ RaYc[ RXD[F RWYT[ RZPbO_R ReCcF R\\Y\\[ RcLfNlQ RZLXNRQ R[NYQUR',
	    'Po^IlI RTO]O R^SlS RTT]T RTITS RWVWZ RYJYS RSK]J]S ReEeN ReHjM ReH_N R\\U]Y RZVZZ RVCRK RVF[FXL RTVS[ RiSgWeY`[ RcP`WjZ RjD_E',
	    'Po^FlF R`NjN RTO\\O RTT\\T R^VlV Ra[e[ RTITS RWVWZ RYJYS RZVZZ RSK\\J\\S R`I`N ReTe[ R`IjIjN RdCeF R\\V\\X RVCRL RUFZFXL RTVS[ R_QjQeS',
	    'Po^IkI RTO\\O RTT\\T R]UkU Rb[d[ RTITS RWVWZ RXJXS RYVYZ RSK\\J\\S RdSd[ R^EeEeN RdH_N R\\V\\X ReHhLlN RVCRL RUFZFXK RTVS[ R_PiQdS RiCaE',
	    'PobIiI RbMjM RTO\\O RTT\\T RcWiW Rh[l[ RTHTS RWVWZ RYJYS RSK\\J\\S RcQcX RfFfM RcQiQiV R`DlDl[ R]V^Y RZVZY RVCRL RUF[FXL RTVS[ R`D`S^Z',
	    'Po^FkF R^MlM RTO\\O R]RlR RTT\\T RTITS RWVWZ RYJYS RZVZY RSK\\J\\S RaHbK RcDfF R]U]X RaVk[ RhHfM RVCRL RUFZFXL RTVS[ RcNaS_ViY RiRgVeY][',
	    'PoaKjK RTO\\O RTT\\T RcViV R_YlY RTITS RYJYS RSK\\J\\S R_E_[ RcPcV ReGeP RcPiPiV R_ElEl[ RZVZZ RVCRL RVF[FXL RTVS[ RWVWZ R]V]Y',
	    'Po`GjG R_KjK RTO\\O RTT\\T RTITS RYJYS RZVZY RSK\\J\\S R^N^Q R_R_Y ReNe[ R_DjDjK R^NlNlQ R]U]X RVCRL RUFZFXL RTVS[ RWVWZ R_RjRjYgY',
	    'Po^GlG R_NkN RTO\\O R_RkR RTT\\T RTITS RWVWZ RYJYS RSK\\J\\S R_J_R ReCe[ R_JkJkR RfTlY RcT]Z R\\V]X RZVZY RVCSL RVF[FXL RTVS[',
	    'Po^GkG RTO\\O RTT\\T RTITS RWVWZ RXJXS RZVZZ RSK\\J\\S ReCe[ RfRlX RhMlP RcS^X RaMdOgT RiJfP RVCRL RUFZFXK RTVS[ RaJ^Q ReObU R\\U\\W',
	    'Po^HbH RgHlH R^NbN RgNkN RTO\\O RTT\\T RgTlT RTITT RXJXT RYVYY RSK\\J\\T RgCg[ RaW][ R\\U]X RVCRK RUFZFXL RVVWZ RTVS[ RcCbT]T RbPaX',
	    'Po_KcK RfKkK RTO\\O R_QkQ RTT\\T Rg[l[ RTITS RWVWZ RYJYS RSK]J]S R_E_Q RgQg[ RfEkEkQ RlXl[ RaW][ R\\V]Y RYVZZ RVCRL RVF[FXL RTVS[ RdD_F RbQ`Y',
	    'Po_IjI R_MjM RTO\\O RTT\\T RTITS RYJYS RZVZY RSK\\J\\S R_D_M R_DjDkM RaP]S RcT^Y R\\V\\X RhQdZ RVCRL RUF[FXL RTVS[ RdQaV RWVWZ Rk[g[ RbM`RlRk[',
	    'Po_IjI R_MjM RTO\\O R`SdS RTT\\T Rf[l[ RTITS RYJYS RSK\\J\\S R_D_M R`O`Z RgOg[ R_DjDjM RlXl[ R\\U]X RYVZY RVCRL RUFZFXL RTVS[ RkRfT RWVWZ RdY]Z',
	    'Po_GkG R^KlK RTO\\O RTT\\T R_UkU R]ZlZ RTITS RWVWZ RYJYS RSK\\J\\S ReCeK ReQeZ RgKgPlOlQ R\\V]X RYVZY RVCRL RUF[FXL RTVS[ RbK`P^P',
	    'PoTO[O RTT[T R^ZjZ RTITS RVVVZ RXJXS RXVYY RSK[J[S R^S^[ RfDfN R^SjSj[ RfHkJ RbL]Q R[V[Y RUDRK RUFYFWL R`C]J R_FdF`O RSVS[ R_GaLdOkP',
	    'Po`IkI R`MkM RTO]O RTT]T R_UlU R_ZlZ RTITS RYJYS RSK]J]S R_Q_[ R`E`M R`EkEkM R_QlQl[ RZVZY R\\U^X RVCSK RVF[FXL RTVS[ RWVWZ',
	    'Po^GlG RTO\\O R`QkQ RTT\\T Ra[e[ RTITS RWVWZ RYJYS RSK]J]S R`K`Q ReQe[ R`KkKkQ R\\V\\X RYVZY RiTlY RVCRK RVF[FXL RTVS[ R`T^Y ReCeG',
	    'Po`KhK RTO\\O RTT\\T R^TjT RTITS RWVWZ RXJXS RYVYZ RSK\\J\\S R^O^[ RbOb[ RfOf[ RdDkL RcE]L R\\V\\X RVCRL RUFZFXL RTVS[ R^OjOj[h[',
	    'Po_FjF R^JlJ RTO\\O RaRiR RTT\\T RTITS RWVWZ RYJYS RSJ\\J\\S ReCeJ RgJgOkNkO RcQ^U R\\V\\Y RYVZY RaSfXl[ RVCRL RUFZFXL RTVS[ RbJ`O]P RiSdX^[',
	    'Po[EkE R]KcK RTO[O R]QcQ RTT[T RTITS RVVVZ RXIXS RXVYZ RSK[J[S R]E]X RcEc[ RhVdZ RZV[Y RgRjZ RjPfY RVCRK RUFYFWL RSVS[ RbV\\W ReKhV RcIkIiV',
	    'PoRH_H RgOkO RYPeP RVUjU R_R_W RSRjRjW RVQVWiXk[ RbYc[ RWYR[ RfJbN RYHRN R\\Y\\[ RYJ^L RcC`J RgFdL RYDZOTR RbGlGkO R]CSE',
	    'Po^KlK RTO[O RbPeP RTT[T R]ZlZ RTITS RVVVZ RXJXS RYVYY RSK[J[S R_R_Y RcRcY ReHeO RgRgY R_RjRjY R`EjEfH R[V[X RUDRK RUFYFWL RSVS[',
	    'Po`LjL RTO]O R^PlP RTT]T Rb[e[ RTITS RWVWZ RYJYS RSK]J]S R_G_J ReCeG RePe[ R_GlGlJ R\\U]X RZVZY RiTlY RVCRL RVF[FXK RTVS[ RaT^Y',
	    'Po^HeH R\\LfL RTOZO RTTZT Ri[l[ RTITS RVVVZ RXIXS RXVXZ RSKZJZS R]P]Y RaCa[ RhFhU RlCl[ R[V\\Y RVCRK RUFYFWM RSVR[ R^D]J R]PePeXcX',
	    'PoaJhJ RTO\\O RTT\\T Rc[h[ RTITS RYJYS RSK\\J\\S RcTc[ RhXh[ ReCjI RdC^J R\\V\\X RYVZZ RjUlZ RcRgS R_MiMgQ RVCRL RUFZFXL RTVS[ R_U^Z RWVWZ',
	    'PodFkF RaMgM RTO\\O ReQkQ RTT\\T RTHTS RXIXS RYVYZ RSK\\J\\S RdCdI ReJeP R_IlIkL RhSh[lZl[ R[V\\Y RVCRK RUFZFXL RTVR[ RVVWZ RcSbZ R_I_T^Z',
	    'Po\\HdH RTO[O RTT[T RTITS RVVVZ RXJXS RXVYY RSK[J[S RcMcZ RfVk[ RfOjR R[V\\Y RVCRK RUFZFWK Rb[^[ RhCkK R^McM RSVR[ RgCeL R_C_H^Q]Y',
	    'Po_FlF RTO\\O RbOlO RTT\\T R`ZhZ RTHTS RXIXS RYVYZ RSK\\J\\S RbJbW ReCeG RlWlZ R\\V\\X RVCRK RUFZFXL RTVR[ RVVWZ RfV`W RhIhPiX RjIbJ R_F_S][',
	    'PoTO\\O R_SfS RTT\\T RTITT RWVWZ RYJYT RYVYZ RSK\\J\\T R_M_S RbHbY R_MfMfS RlWlZ R\\V\\Y RVCRL RUFZFXL RTVS[ RfH^I R^EiEjUjZ RfUgY^Y',
	    'Po^IkI R^MlM RTO\\O RTT\\T RTITS RYJYS RSK\\J\\S RfMlR RhFkI RdQ`S RcM^R R\\V]Y RYVZZ RVCRL RUFZFXL RTVS[ RcCaH RdHaP RfT`V RWVWZ RiWdY^[',
	    'Po_IkI RTO\\O R_PkP RTT\\T R_TkT R^WlW RTITS RWVWZ RYJYS RYVYZ RSK\\J\\S R^E^H R_M_T ReGe[ R_MkMkT R^ElElH R\\U\\X RVCRL RUFZFXL RTVS[',
	    'Po`HjH R`LjL RTO\\O R^PlP RTT\\T ReTkT Rd[l[ RTITS RWVWZ RYJYS RSK]J]S R`D`L RePeZ R`DjDjL R\\V]X RYVZY R`TbY RVCSL RVF[FXL RTVS[ R`R^Z',
	    'PobJlJ RcOkO RTO]O RbSlS RTT\\T RTHTS RXIXS RYVYZ RSK\\J\\S R`G`[ RdDjEiJ RhWl[ ReWb[ R[V]Y RgNgTiY RVCRK RUFZFXL RTVS[ RdKbQ RVVWZ RaC^M RfPfX',
	    'Po`HjH R`LjL RTO]O R`RkR RTT]T R`UkU R^ZlZ RTITS RWVWZ RYJYS RSK]J]S R`D`L ReMeZ R`DjDjL R\\V]Y RZVZZ RVCSK RVF[FXK RTVS[ RaN_T',
	    'Po_GhG R^LlL RTO\\O RTT\\T RaUjU RaZjZ RTITS RWVWZ RYJYS RSK]J]S RaQa[ RdCdL R`QjQj[ RgNiP RiH`R R\\V]X RYVZY RjEhJ RVCSL RVF[FXL RTVS[',
	    'Po\\GfG RTO[O R]QeQ RTT[T Ri[l[ RTITS RWVWZ RXJXS RYVYY RSK[J[S R]K]Q RaCa[ R]KeKeQ RhFhT RlCl[ RaReV R[U[X RUDRK RUFYFWM RaP]X RSVS[',
	    'Po_LkL RTO\\O RTT\\T RTITS RWVWZ RYJYS RZVZZ RSK\\J\\S R_E_L RkEkL RhNkQ RbEdFhJ RaM^Q RcP^U RhDbI R]U]X R`SeXl[ RVCRL RUFZFXL RTVS[ R`SiSeX^[',
	    'Po]KdK RTO[O R]PdP RTT[T RiVlV R^ZdZ RTITS RVVVZ RXJXS RXVYZ RSK[J[S R]G]P R^Q^Z R]GdGdP RdWdZ RfEf[ RfElElV R[V[Y RVCRK RUFYFWM R`C_G RcR]U RSVR[',
	    'PobElE RbMkM RTO[O RbQkQ RTT[T RTITS RVVVZ RXJXS RYVYY RSK[J[S R_G_[ RbIbQ RbIkIkQ RaTfXa[ R[V[X ReXl[ RUDRK RUFYFWL RSVR[ R_D^L RfEfReX',
	    'Po_MkM RTO\\O RTT\\T RaUlU RTITS RYIYS RYVYY RSK\\J\\S R_E`Sa[ RePe[ RhPh[ R_HkHkM R\\U]X RVCSL RUFZFXL RTVS[ RWVWZ RaPlPl[j[ R_O^Z ReE_E RjDaE',
	    'Po_KjK RTO\\O R^OlO RaSjS RTT\\T RTITS RYJYS RSJ\\J\\S RdFeJ RjFkJ R\\V]Y RYVZZ RaSfYl[ RVCRL RUFZFXL RTVS[ R`E^J RaS^[ RiTeY`[ RWVWZ RbK`X RiD`E',
	    'Po_JjJ R_OjO RTO\\O RTT\\T Ra[d[ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S R_F_O ReOe[ R_FjFjO RfTlZ RbU]Z RkQgU R\\U\\X RePhW RVCRL RUFZFXK RTVS[ RdCdF R^RcR`W',
	    'Po_JdJ RTO\\O R_OdO RTT\\T R_TdT Ri[l[ RTITS RWVWZ RYIYS RYVYZ RSK\\J\\S R_D_T R_DdDdT RhFhU RlCl[ R\\V\\Y RcVeZ RVCRK RUFZFXL RTVS[ R`V^[',
	    'Po_IjI R_NjN RTO\\O R^QlQ RTT\\T RTITT RWVWZ RXJXT RZVZZ RSK\\J\\T R_D_M R`Q`[ ReDeM R_DjDjM RfVk[ RkShV R\\U\\X RdQgX RVCRL RUFZFXL RTVS[ ReY_Z',
	    'Po^HlH R`KeK RTO\\O RTT\\T RTITS RXIXS RYVYY RSK\\J\\S RlWk[ RaTeX RiDkF R[V]Y RhChOiRfZ RVCRK RUFZFXL RTVR[ RbY_Z RVVWZ RbMaU R`PePcX RkKhSi[ R^H^T]Z',
	    'Po_IkI R_MkM RSO\\O RST\\T Ri[l[ RTITS RYIYS RYVYY RSK\\J\\S R^Q^[ R_D_M ReDeV R_DkDkM R^QlQl[ R\\U\\X RVCRL RUFZFXM RVVWZ RSVS[ RhSjWaW',
	    'Po^FkF R_IkI R^MlM RTO\\O RTT\\T RaViV RaZiZ RTITS RYJYS RSJ\\J\\S RaPa[ R`QiQi[ RhOlS RaO]S RYVZZ RfIjQ RVCRL RUFZFXL RTVS[ RdI`R RWVWZ R\\V]Y RdCbN',
	    'PoTO\\O RbOeO R^SlS RTT\\T RTITS RWVWZ RYJYS RZVZY RSK\\J\\S ReQe[ RfHfN ReRlY ReI`N R_IkIjM RdS^Y R\\V\\X RbFhI RVCRL RUFZFXL RTVS[ R_EjEdG',
	    'Po]GlG RdKiK R`OkO RTO\\O R^SlS RTT\\T RTITS RYJYS RYVZY RSK\\J\\S R`D`O RdCdK ReOe[ RiCiK RfTlZ RcT^Z R\\U]X RVCRL RUFZFXL RTVS[ RWVWZ',
	    'Po`JkJ R`NkN RTO]O R_QkQ RTT]T R_VkV R^ZlZ RTITS RWVWZ RYJYS RSK]J]S R`F`N ReQeZ R`FkFkN RdCcF R\\V]Y RZVZZ RVCRK RVF[FXK RTVS[',
	    'Po^FlF RTO\\O R_RkR RTT\\T RTHTS RWVWZ RYJYS RSK\\J\\S R_J_R RbLbP ReCe[ R_JkJkR RgUlZ RePcT]Z R\\V\\X RZVZZ RePgU RhLgP RVCRL RUFZFXL RTVS[',
	    'Po^DlD RaJjJ RaMjM RTO\\O R`PlP RTT\\T R^YlY RTITS RWVWZ RYIYS RSK\\J\\S R^D^[ RaGaM RaGjGjM R\\V\\Y RYVYZ RbSkW RVCRL RUFZFXL ReMbT RiPgUaW RTVS[',
	    'PoTO\\O RfRkR RTT\\T R_VkV R_ZkZ RTITS RYJYS RSJ\\J\\S R_M_[ RcJcQ RaJhJgR R_MkMk[ RYVZZ RbEhEjJ RVCRL RUFZFXL RTVS[ R`F^K RWVWZ R\\V]Y',
	    'Po]KcK RTO[O RTT[T RTITS RVVVZ RXJXS RXVYZ RSK[J[S RaEa[ R`OcR R[V\\Y RhTk[ RgTc[ RVCRK RUFZFWK RhCgQjY RgNfX RkIjO RSVR[ R`J]U RcD\\E RhMiW ReIdP',
	    'PoaJlJ RbMjM RTO[O RbQkQ RTT[T RaUlU Rd[l[ RTITS RVVVZ RXJXS RYVYY RSK[J[S RfCfY RbFjFkM R[U[X RUDRK RUFYFWL R]Q_U\\[ RSVS[ R\\E`E\\M`M^WbYj[',
	    'Po^ElE RaMjM RTO\\O RTT\\T R_UkU R_ZkZ RTITS RWVWZ RYJYS RSK\\J\\S R_P_[ RaHaM RePeZ RaHjHjM R_PkPl[ R\\V]Y RZVZZ RVCRL RUFZFXL RTVS[',
	    'Po^JcJ RfJkJ R_MjM RTO\\O R^PlP RTT\\T Rd[h[ RTITS RYJYS RSK\\J\\S R^D^J R^DcDcJ RfDfJ RfDkDkJ R\\V]Z RYVZZ RVCRL RUFZFXM RTVS[ RWVWZ RbQaUjTh[',
	    'Po`HjH R`LjL RTO\\O R^OlO RTT\\T Rf[j[ RTITS RYJYS RSK\\J\\S R`D`L R`DjDjL RbV^Z R\\U]X RYVZY RhRdZ RVCRL RUF[FXL RTVS[ RaO_T RcRaX RWVWZ R`SkSj[',
	    'Po`GkG R`MjM RTO\\O R`PjP RTT\\T RTITS RYJYS RSK\\J\\S R`I`P R`IjIjP RcP^V R\\V]Y RYVZZ RaTdXk[ RVCRL RUFZFXL RTVS[ RaC_I RaSjSfW^[ RWVWZ',
	    'Po_JkJ RTO\\O R_OkO RTT\\T Rc[h[ RTITS RYJYS RSK\\J\\S R_E_O RcTc[ ReEeO RhXh[ R_EkEkO R\\V\\X RYVZZ RiTlY RcQgS RVCRL RUFZFXL RTVS[ RWVWZ R_U^Z',
	    'Po^HlH R`KeK RTO\\O RTT\\T RaUeU RTITS RXIXS RYVYY RSK\\J\\S RaOaW RaOeOeU RiDkF RhUc[ R\\V]Y RlWj[k[ RjNfX RVCRK RUFZFXL RTVS[ RVVWZ RkLhU RhDhPiZ R^H^T]Z',
	    'Po^KcK RfLkL RTO[O R^PcP RTT[T R^UcU RTITS RVVVZ RXJXS RXVYZ RSK[J[S R^D^[ R^DcDcK RfDkDkK RhWl[ RjUdZ R[V\\Y RfRiY RdPkPhX RVCRK RUFZFWK RSVS[',
	    'Po^FlF RbIjI R`MlM RTO\\O R`SjS RTT\\T R`WjW Rg[j[ RTITS RYJYS RSJ\\J\\S R`P`[ R`PjPk[ RZVZZ RVCRL RUF[FXL RTVS[ RcC_K RWVWZ R\\V]Y RfIfM',
	    'Po]IeI RfKlK RTO[O RfSlS RTS[S R^WdW Ri[l[ RTITS RWVWZ RXJXS RYVYY RSK[J[S R^P^Y RaCaO RfEfV RfElEl[ R[U[X RVCRK RUFYFWL RSVS[ R^PdPeSeY',
	    'Po_KkK RTO\\O R_PkP RTT\\T R`UkU R^[l[ RTITS RWVWZ RYJYS RSK\\J\\S R^F^I ReCeG ReQeZ R^FlFkI RgMkQ R\\V]X RYVZY RVCRL RUF[FXL RTVS[ RcKaP',
	    'Po^FlF R^NlN RTO]O RTT]T RbZkZ RTITS RYJYS RSK]J]S RbQb[ RcCcJ RhChJ RaSkSk[ R\\V]X RZVZZ RVCSK RVF[FXL RTVS[ RdL`U RWVWZ',
	    'PoaJjJ RTO]O R`RjR RTT]T R`WjW R^ZlZ RTITS RWVWZ RYJYS RSK]J]S R^G_J R`N`W ReCeG R`NjNjW R_GlGlJ R\\V]X RZVZZ RVCSL RVF[FXK RTVS[',
	    'Po_IjI R_MjM RTO\\O RTT\\T R\\ZlZ RTITS RYJYS RSK\\J\\S R_Q_Z R_D_M RcQcZ RgQgZ R_DjDjM R_QkQkZ R\\U]X RYVZY RVCRL RUF[FXL RTVS[ RWVWZ',
	    'Po]HlH RTO\\O R]OlO R^SjS RTT\\T RTITS RXIXS RYVYZ RSK\\J\\S RbDb[ RgEg[ R^KjKjS RgUlY R`V\\Z R[U\\X RVCRK RUFZFXL RTVR[ RbR_X RjDfD RVVWZ RdD^E',
	    'Po^FlF RaMjM RTO\\O RTT\\T RbWhW RTITS RYJYS RSJ\\J\\S R_P_[ RaIaM RbSbY ReCeF RbShShW RaIjIjM R\\V]Y RZVZZ RVCRL RUF[FXL RTVS[ RWVWZ R_PlPk[i[',
	    'Po`KjK RTO\\O R`OlO RTT\\T Rf[j[ RTITS RYJYS RYVZY RSJ\\J\\S RbUbY R`FjFjK RgTiV RdCcF R\\V\\X ReUfX RVCRL RUF[FXL RTVS[ R_V^Z RWVWZ R`F`SkSj[',
	    'Po_FlF RTO\\O R^PlP RTT\\T Re[i[ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S R_K_N ReMfP R_KlKlN RdCeF R]U]X RbGbJ RVCRL RUFZFXL RTVS[ RhGgK R`Z][ RcTjTi[ RcPaX',
	    'Po_EkE RTO\\O RTT\\T R_UkU R^ZkZ RTITS RWVWZ RYJYS RSK\\J\\S R_P_U ReMeZ R_PkPkU RiWlZ RdFfH RZVZY R]U]X R`FeKkM RVCRL RUFZFXL RTVR[ RjFfK^N',
	    'PoTO\\O RTT\\T R_UkU R_ZkZ RTITS RYIYS RYVYZ RSK\\J\\S R_E_M R_Q_[ ReQeZ R_QkQk[ RVCRL RUFZFXL RTVS[ ReNcO ReElElNhN RdD_E RVVWZ R\\V]Z RcHdL]M RhEfM',
	    'Po`EkE R`IkI R`MkM RTO\\O RTT\\T RTITS RYJYS RZVZY RSK\\J\\S RcUcY RfEfQ RhTjV R\\V]X RfTfX RVCRL RVF[FXL RTVS[ RWVWZ R_U_Z Rk[g[ R`E`QlRk[',
	    'PodGkG RdMlM RTO[O RdQlQ RTT[T RTITS RVVVZ RXIXS RYVYZ RSK[J[S R^E^X RhChL R[V\\Y ReTfW RVCRK RUFZFXL RTVR[ R^MbM^M RbE^EbEbV^V RjNj[g[',
	    'PocElE R]MaM RTOZO RTTZT R]XbX RTITS RVVVZ RWIWS RXVXZ RSKZJZS R]G]Z R_C^GaGbM R]QbQbY RdJdV RhEh[ RZV[Z RVCRK RUFYFWM RSVR[ RdJkJkViV',
	    'Po^JkJ RTO\\O RTT\\T RTITS RYJYS RYVYY RSK\\J\\S R^E^J RbEbJ RdLd[ RfEfJ R^EkEkJ RhVlZ RgMkQ RaM]Q RaV]Z R\\U]X RfQiX RVCRL RUFZFXL RTVS[ RaS_X RWVWZ',
	    'Po\\FlF RTO[O RTT[T RTITS RVVVZ RXJXS RXVYY RSK[J[S RaCaI RgCgI R\\O_Q`T RdOhR R[V[X RhJfP RjOeZ RVCRK RUFZFWL R`J_Q RbO]Z RTVR[ RjVlZdZ RbVcZ\\Z',
	    'Po^GiG R\\KlK R`OeO R^TiT R^WiW RV[Z[ RTEZEZ[ R_P_W RdPdW R]QiQiW RiXl[ RVIXK ReXf[ RVPXR RfJlO RhDkH RbM]Q R^Y\\[ R^D]H RaJ[O RbYb[ RfGhL RcD_M RTETSS[',
	    'PobHiH RbKiK RTO[O RTT[T Ra[d[ RTITS RVVVZ RXJXS RYVYY RSK\\J\\S R_D_L R^OdOd[ R_DlDlL RfOlOl[ RaP^T RbU^Y RjPfT RjUfY R[V\\X RVCRK RUFZFXL RTVR[ Ri[l[i[',
	    'PoTO\\O RaPkP R^SlS RTT\\T R`ZkZ RTITS RYJYS RSJ\\J\\S R`U`[ RbM`Q ReOeZ RkUk[ R`HcL RbGeH RdC_H RYVZZ RVCRL RUF[FXL RTVS[ RbFjFeK^N RWVWZ R\\V]Y',
	    'Po_FjF R^KlK RTO\\O R^PkP RTT\\T R]ZlZ RTITS RWVWZ RYJYS RSK\\J\\S R_S_Z RcScZ RgSgZ R_SjSjZ RhMkQ R\\U]X RZVZZ RVCRL RUFZFXL RTVR[ ReCdK`P',
	    'PoTO\\O RTT\\T R`ZjZ RTITT RWVWZ RYJYT RZVZZ RSK\\J\\T R^F^J R`S`[ R`SjSj[ R^FlFlJ ReLjR RgIkM RdCeF ReL^S RbI_M R\\V]X RVCRL RUFZFXL RTVS[',
	    'PodEkE RaLhL RdOkO RTO\\O R`RlR RTT\\T RTITT RXJXT RYVYY RSJ\\J\\T RdCdH RdIdN R^HlHkK RePgR R[U]Y RcRdVgYl[ RVCRK RUFZFXL RSVS[ RiTeX_[ RVVVZ R^H^S][',
	    'Po^FgF R^IlI RTO^O RaQlQ RTT\\T R`VkV R`ZkZ RTITS RYJYS RSJ\\J\\S R`S`[ RbLbP RcCcI R`SkSk[ RYVZZ RVCRL RUF[FXL RTVS[ RWVWZ R\\V]Y RkDcK^N[O RjLaM',
	    'Po_NjN RTO\\O RTT\\T R\\ZlZ RTITS RYJYS RSK\\J\\S R_Q_Z R_D_N RcQcZ RgQgZ R_DjDjN R_QkQkZ RdIhL R\\U]X RYVZY RVCRL RUF[FXL RTVS[ ReFcL RWVWZ',
	    'Po^JcJ RgJlJ RTO[O RTT[T R_[b[ Rg[k[ RTITS RWVWZ RXJXS RYVYZ RSK[J[S R]DcDcI RfDlDlI RaP^S RbT^X RiPfS RjTfW R[V\\Z RVCRK RUFZFWL RTVR[ RgJfOlNlVk[ R^J^OdOdVc[',
	    'Po]EcE R\\JdJ ReNlN RTOZO RTTZT ReTlT RTITS RVVVZ RXIXS RXVXZ RSKZJZS R]N]W R`J`[ RcNcW ReHeV RiCi[ ReHlHlU R[V[Y RVCRK RUFYFWM RSVR[',
	    'PoaFkF RbMkM RTO[O RbQkQ RTT[T RaUlU Rb[l[ RTITS RVVVZ RXJXS RXVXZ RSK[J[S RbJbQ RfCfY RbJkJkQ R]J_L R]D_F R\\P`P_X\\Z R[V[Y RVCRK RUFYFWL RSVR[',
	    'PoTD]D RbGlG RRG^G RRI^I RXPbP RVThT RVWhW RWQWW RXCXI R_R_W RURhRhW RaYc[ RVKRO RcE_H RVL]K\\N RbHgMlO RgYl[ RZPRS RiHeL_N RVYR[ R[Y[[ RYN\\NYN',
	    'Po\\FeF RTOZO R]RdR RTTZT R\\VeV Rg[k[ RTHTS RWVWZ RSKYJXS RXVYY RZJZS R]M]R R_C_JbJa[ RcCcJ R]MdMdR RZV[X RVDRK RUFYFWL RSVS[ RiChQeZ ReIlIlVkZ RhKgW',
	    'PoZHcH RYJcJ R[M`M RZScS RZVcV RVKV[ RZDZH RZOZV R]X\\Z R^D^H R_O_V RcDcH RYPcPcV RhVc[ RWCRH RVLRP R]LXQ R`X`Z RbWcY RgSkZ RjQfX RWHUM RYXY[ RgGfNiX RfJhW RfIlIhV RgCeN',
	    'PoTE_E RbFlF RRJ`J RTM_M RXQbQ RVUiU R_R_W RTSiRiW RXKZK RaYc[ RUDRG R[Y[[ RbFdJgMlO RVRVWhXl[ RcCaH R]NYPSS RiHeL`N RVYR[ RTGTN RUG^G]O',
	    'PoaNhN RTO\\O R^SkS RTT\\T Ra[d[ RTITS RWVWZ RYIYS RSJ]J]S RdSd[ RjVkZ R_GcI RhLlO R]IaK]O R\\U\\X RYVYZ RfFiN RgFkFhL RVCRK RUFZFXL RTVR[ R`C]J R_FdF`M R`U^Y ReDhL',
	    'PoTOZO RbRkR RTTZT RTHTS RVVVZ RXJXS RXVXZ RSKZJZS R^H^[ R`H`V RgOg[ RiVlY ReIjN RiIcO RdVaY R]LZO R[V[Y RgQiW RdGfL RdGjGgL RgQdW RVCRK RUFYFWL ReCbJ RSVR[ R_C]L',
	    'Po_ElE RcNiN RTO\\O RTT\\T RaWeW RgWkW R_ZlZ RTITT RWVWZ RYJYT RZVZZ RSK\\J\\T R_E_Z RaPaW RcHcN RaPePeW RgPgW RcHiHiN RgPkPkW R\\V]X RVCRL RUFZFXL RTVS[',
	    'Po^EdE R^LdL RTO[O R^PdP RTT[T R^UjU R][l[ RTITS RVVVZ RXJXS RXVXZ RSK[J[S R^E^Q R^HdHdL RdRdZ RiLdQ R[V\\Y RgHjP RVCRK RUFZFWK RaLaP RaEaH RfFiN ReEkEiM RTVR[',
	    'Po^HjH RfMlM RTO\\O RTS\\S RTITS RWVWZ RXIXS RYVYZ RSJ\\J\\S ReKiPlQ RjKlM RhEkH RdK^Q R\\V\\Y RhIgN RVCRK RUFZFXL RbCaG R_I^M RdQ_S RgS`V RTVS[ RiWdZ^[ RbKcM]M',
	    'Po`GjG R`JjJ RTO\\O R^QkQ RTT\\T RTITS RYJYS RSK\\J\\S R^M^Q R`D`J R`DjDjJ R^MkMkQ R\\V]Y RYVZZ RaTdXk[ RVCRL RUFZFXL RTVS[ R^TjTgX_[ RWVWZ RbMcQ RgMgQ',
	    'PoTO\\O RTT\\T R`UkU R`ZkZ RTITS RWVWZ RYJYS RZVZZ RSK\\J\\S R`Q`[ R^DdDdM ReNeQ R`QkQl[ RfDlDlM R_FaI RgFiI R]V]X RVCRL RVF[FXL RTVS[ RdJ^L RlIfL',
	    'Po]FlF R^MkM RTO\\O R^PkP RTT\\T R`WjW R`ZjZ RTITS RYIYS RSK\\J\\S R^I^P R`S`[ RcCcP RgCgP R`SjSj[ R^IkIkP R\\V]Y RYVZZ RVCRL RUFZFXL RWVWZ RTVS[',
	    'Po^ElE R_MkM RTO\\O R_PjP RTT\\T R^TlT Rb[e[ RTITS RYJYS RYVZY RSK\\J\\S R_H_M RcEcM ReTe[ RgEgM R_HkHkM RiVlZ RaV]Z R\\U]X RVCRL RUF[FXL RTVS[ RWVWZ',
	    'Po]EkE R^HjH R]KlK RTO\\O R_QjQ RTT\\T R_TjT R_WjW RTITS RWVWZ RXJXS RZVZY RSK\\J\\S R_M_W RdCdK R_MjMjW R\\U]X RgYl[ RVCRL RUFZFXL RTVS[ RbY\\[',
	    'Po`DkD RTG]G R`JlJ RTJ]J RXQdQ RUUiU RTDTO RTD]D]J R_R_W R`F`I RSRiRiW RfOlNlO R[K^N RVQVWhXj[ RaYc[ RVYR[ R[Y[[ RfKfO RZOSR ReJbM_N RfEdL R\\MRN',
	    'PoaElE RTOZO RTTZT Rb[l[ RTITS RVVVZ RXJXS RXVXZ RSK[J[S RgOgW R\\Q`Q_XbZ R]J_M R]E`G ReLaP R_W\\Z RgOaU RgEaK R[V[Y RlIhMkT RVCRK RUFYFWL RSVR[ RgWdX RfKgT RgEiQ',
	    'PoTOZO RcQlQ R\\QaQ RTTZT Rf[i[ RTHTS RVVVZ RXJXS RXVXY RSKZJZS RaCa[ RiMi[ RdJgL RfHjIdO RfEbI RZU[X RdSfW ReFkFhK RUFYFWL R]E]LaL RUCSK RSVS[ R]S\\[',
	    'Po^GkG R_KkK R]NlN RTO\\O RTT\\T R`UlU R_ZlZ RTITS RXIXS RYVYY RSK\\J\\S RdGdN RaRiQiY RaCaF RhCgF R\\U\\X RVCRK RUFZFXL RTVR[ RVVWZ RaN]Y RdQcZ',
	    'Po^ElE R_KkK RTO\\O R^RkR RTT\\T R^UlU RTITS RYJYS RSK\\J\\S ReCeR RhSh[ R_HkHkN R`WcY R_H_NjOlR R\\V]X RZVZY RVCSK RVFZFXL RTVS[ RWVWZ Re[h[e[',
	    'Po^FlF R`LlL RaNjN RTO[O RTT[T R`TkT R`WkW RTITS RVVVZ RXIXS RYVYZ RSK[J[S R`Q`[ RfCf[ RaIjIjN R[V[X RVCRK RUFZFXL RTVR[ R`QkQk[h[ R^F^S\\[',
	    'Po_FkF R^JlJ RTO]O R`PkP R`SkS RTT]T R^VlV RTITS RWVWZ RYJYS RSK]J]S R`M`S RaGbJ ReCeF ReSe[ R`MkMkS R\\V]Y RZVZZ RVCRK RVF[FXK RTVS[ RiFgJ',
	    'Po^FlF R`MlM RTO\\O RaQjQ RTT\\T Rb[e[ RTITS RXIXS RYVYZ RSK\\J\\S ReCeF ReGe[ RaJjJjP RfUkZ R`SbU RkSgV R\\V\\Z ReQhW RVCRK RUFZFXL RTVR[ RcW_Y RVVWZ R^F^T]Z',
	    'Po_ElE RaKcK RgKjK RTO\\O R`OcO RgOjO RTT\\T R`VkV R`ZkZ RTITS RYJYS RSK\\J\\S R_H_K ReEeP R`RkRk[ R_HlHlK R\\V^Y RZVZZ RVCRL RVF[FXL RTVS[ RWVWZ',
	    'PobGlG RVThT RVWhW RTGTM RWPWW RYCYM R`Q`W RUQhQhW RaYc[ R\\D_G RYObO_R RUDRG R[Y\\[ R[I[K RbGfKlM RgYkZ RVIUK RcCaH RZOSR RiGfKaM RVYR[ RTG^G^M\\L',
	    'PoTO\\O RgSkS RTT\\T RbYhY RTITS RYIYS RYVYZ RSK\\J\\S R_O_[ RbUbZ R^IlIiL RfOfS RbFhI RVCRL RUFZFXL RTVS[ RcObS R_EjEeG RdJ]M RVVWZ R\\V\\Z RbUhUhY R_OkOk[h[ ReIeNbN',
	    'PodFkF RaLgL RTO\\O RTT\\T R_ZlZ RTITT RWVWZ RYJYT RYVYZ RSK\\J\\T RaSaW RdCdI RdQdZ RgQgZ R_IkIjK RdJdOjNjO RVCRL RUFZFXK RTVS[ R\\U\\W RjSjW R_I_Q]Z',
	    'Po_MkM RTO[O RTT[T RTITS RXJXS RYVYY RSK[J[S R`H`M RgSgZ R^IkIkM RbE]J R[U\\X RjQgRkZ RVCRK RUFZFXL RSVS[ ReLcP^R ReR]U RhRdV]Y RVVVZ Rf[b[ RaFhEfIdNfSgX',
	    'PoSO[O RiQkQ RST[T RTHTS RVVV[ RXIXS RSK[J[S ReLiLiQ RfVk[ RdUfW RgGkL RiDfF RkGhH RZV[Y RXVYZ ReChI RUDRK RUFYFWL R^DcDaI RdSjSiWgYc[ RSVS[ Ra[^[ R^GbG]LbLbP^P]UbUa[ ReLdQ',
	    'Po^HlH RaOiO RTO\\O RTT\\T RbXhX Rg[k[ RTITS RXIXS RYVYY RSK\\J\\S R^Q^[ RbJbO RbTbY RbThThX R`LiKiO R^QkQk[ RfHkM RcH^M R\\V\\X RVCRK RUFZFXL RTVR[ R^EdEaK RVVWZ RiDaD',
	    'Po]DlD RcKgK RTO\\O R_QkQ RTT\\T R_TkT R]WlW RTITS RXIXS RYVYZ RSK\\J\\S R_H_L R_N_T RcDcK ReTe[ RgDgK R_NkNkT R_HkHkL R\\V\\Y RVCRK RUFZFXL RTVR[ RVVWZ',
	    'Po]HlH R_KjK RTO[O RTT[T R^TkT RTITS RVVVZ RXJXS RYVYZ RSK[J[S RbWb[ RdCd[ RgWg[ R_EjEjK RkNk[ R[V\\X RVCRK RUFYFWL RgNgRkR RTVR[ RbNbR^Q RbW^W RkWgW R^N^U]Z',
	    'Po^FkF R]JlJ RTO[O R_RkR RTT[T R_VkV RTITS RWVWZ RXJXS RYVYY RSK[J[S R_N_V RaCaJ ReJeV RhChJ R_NkNkV RgXkZ R[V\\X RVCRK RUFZFXL RTVS[ RbX][',
	    'PogMkM RTO\\O R_PkP R_RkR RTT\\T R^UlU Rd[g[ RTITS RYJYS RSJ\\J\\S R_J_R RcGcM RaGhGgM RhRh[ R_JkJkR RaWcY RbDhElH RaE]I RYVZZ RVCRL RUF[FXL RTVS[ RWVWZ R\\V]Y',
	    'Po_IdI RfIkI RTO\\O R_PkP R_SkS RTT\\T R^WlW RTITS RWVWZ RYJYS RSK]J]S R_E_I R_L_S R_EdEdI ReLe[ RfEfI R_LkLkS RfEkEkI R\\V]X RZVZY RVCSL RVF[FXL RTVS[',
	    'Po^GkG R_JkJ R^MlM RTO\\O R^RlR RTT\\T R`ZjZ RTITS RYJYS RSJ\\J\\S R`U`[ ReGeR R`UjUj[ RaCbG RiNhR RhCgG R\\V]Y RYVZZ RaNaR RVCRL RUF[FXL RTVS[ RWVWZ',
	    'Po]JkJ RTO[O RTT[T R_VjV R_ZjZ RTITS RWVWZ RXJXS RYVYZ RSK[J[S R_R_[ R^EeEeP ReReZ R_RjRj[ ReJkP RdJ^P R\\V\\X R`FaI RVCRL RUFZFXL RSVS[ RiFgJ RjDcE',
	    'Po]ElE R^KeK RTOZO RTTZT R_TdT RTITS RVVVZ RXIXS RXVXZ RSKZJZS R_N_V RdNdU R_G`J RhKiUl[ RhSe[ RVCRK RYFWL RdFcJ RSVR[ RhOjX RgKkKkP RgGeQ RXFUF R]E]T[[ RbKbT`Y RhOgX',
	    'Po^HlH RTO\\O ReQlQ RTT\\T RcWlW RTHTS RXIXS RYVYZ RSK\\J\\S ReCeN ReQeV RiNi[ R^TbU R`DaG RjDhG RbW][ R[V\\Y ReGhKlM RVCRK RUFZFXL RTVR[ RdG_N]U R_RcR`Y RVVWZ',
	    'Po^HlH R_KiK R^NcN RTO\\O ReRkR RTT\\T R^UlU Rd[h[ RTITS RXIXS RYVYZ RSK\\J\\S ReMeQ RhSh[ R_DiDiK RaVcY R\\V\\Z RVCRK RUFZFXL RTVR[ RVVWZ RaNaR ReMkMkR RcQ^R',
	    'Po_FjF R]IlI R]McM ReMkM RTO[O ReQlQ RTT[T R\\TlT Rf[l[ RTITS RVVVZ RXJXS RYVYY RSK[J[S RdCdI RfTf[ RhKhQ RlXl[ R[V[X RUDRK RUFYFWL R`Y[[ RSVS[ RaT`Y R`KaQ\\Q',
	    'Po^FlF R_IkI RTO\\O R`OjO R^SlS RTT\\T R`ZjZ RTITS RYJYS RSJ\\J\\S R`V`[ ReCeI R`LjLjP R`VjVj[ RhQgS R\\V]Y RYVZZ RbQbS RVCRL RUF[FXL RTVS[ RWVWZ R`L`P',
	    'PoTO\\O R_PjP RTT\\T R`ViV R`ZiZ RTITS RYJYS RSK\\J\\S R_H_O R`R`[ RdJdO R`RiRi[ R^JkIjO RaF_J R\\V\\Y RYVZZ RaLbN RcEhEjJ RgKgN RVCRL RUFZFXL RTVS[ RWVWZ',
	    'PobJfJ RbOfO RTOZO RTTZT RbZfZ RTITS RVVVZ RXJXS RXVXZ RSKZJZS RbFbO RbPbZ RcCbFfFfO RfWfZ RhDh[ ReRbU R[V[Y RVCRK RUFYFWL R_Q[R RSVR[ R^D]I^K\\R RjMlRlVjV RhElEjN R`N^X',
	    'Po^FkF R]JlJ RTO\\O R_PjP R_TjT RTT\\T R_WjW RTITS RWVWZ RXIXS RSK\\J\\S R_M_W RaGaL RdCdH RhGhL R_MjMjW R\\U]X RYVZZ RgXl[ RVCRL RUFZFXL RTVR[ RbX][',
	    'Po^IjI R]LlL RTO[O R_SjS RTT[T RTITS RXIXS RYVYY RSK\\J\\S R^E^I R_O_S RaUaZ RbEbI RfEfI R_OjOjS R^EjEjI RfVj[ R[U[X ReSgX RVCRK RUFZFXL RdSaU]W RkUgW RVVVZ RTVR[ RfY^Z',
	    'PoUGZG RdGiG RVJ[J RdJiJ RVTiT RVWiW RSMSP RVEVM RWQWW R[Y[[ R`Q`W RdEiEiM RURiRiW RSMkMkP RaYcZ R]EaF RYObO_R R`D]G RaH]JaJ RgYlZ RZORS RUYR[ RZDUE',
	    'PoaIhI RTO\\O R_QkQ RTT\\T R`WjW R`ZjZ RTITS RWVWZ RYJYS RSJ\\J\\S R_L_Q R`T`[ ReLeQ R`TjTj[ R_LkLkQ ReCkI RdC^J RYVZZ RaNbP RhMgP RVCRL RUFZFXL RTVS[ R\\V]Y',
	    'PobIlI RTOZO RbPfP RTTZT R]TaT RTITS RVVVZ RWIWS RXVXY RSKZJZS R]D]T R]DaDaT RdKdX RlXl[ RiDkF RZVZX R`VaZ RhCiQe[ RVCRK RUFYFWL R]V[[ RSVR[ R]IaI\\J R]OaO\\O RjMgTiZ RhMhT',
	    'Po`IiI RSO[O R^PcP RfPkP R]SlS RST[T RTITS RVVVZ RXJXS RYVYZ RSK[J[S R^K^P R`D`I R^KcKcP RdQd[ RfKfP R`DiDiI RfKkKkP RdSkY RdS^Y R[V[X RVDRK RUFYFWL RSVR[',
	    'Po]FlF RbMgM RTO\\O R_PkP RTT\\T R`UjU R`XjX R][l[ RTITS RWVWZ RYJYS RSK\\J\\S R_H_P R`R`X RbJbM ReCdF RbJgJgM R`RjRjX R_HkHkP R\\U]Y RZVZZ RVCRL RVFZFXL RTVS[',
	    'Po^GlG R`IdI RSO[O R`QdQ RST[T Ra[h[ RTITS RVVV[ RXJXS RSK[J[S R`L`P RaUa[ R`LdLdP RiXh[ RkQlS RcTfW R[V[Y RXVYZ RjVkZ RhDkE RjIgR RVDRK RUFYFWL R^V]Z RSVR[ RfEhQ R^G^N]S',
	    'PoeFjF R]IlI R^MlM RTO[O R`PeP RTT[T RTITS RXIXS RYVYY RSK[J[S R`E`I ReCeI RlXl[ RbPcW_[ RhVc[ R[V[X RhKjL RdSbX RjPgY RVCRK RUFYFWL R^M_U_W RVVVZ RTVR[ RgJgOhY R^P\\Z',
	    'Po^IbI RfIjI R]MbM RTO[O R]QbQ RfQkQ RTT[T R]VbV RfVkV Rc[l[ RTITS RVVVZ RXJXS RXVYY RSK[J[S R]M]V R^E^I RcEcZ RfEf[ R^EjEjI RfMkMkV RlXl[ R[V[X RUDRK RUFYFWL RSVS[',
	    'Po_IkI R_MkM RTO\\O R^OlO RTT\\T R`VjV R^[l[ RTITS RYJYS RSK\\J\\S R_E_L R`R`V RcCcL RgCgL R_FkEkL R\\V]Y RYVZZ RbXbZ RhWg[ RVCRL RUF[FXL RTVS[ RWVWZ R`RjRjV',
	    'PoTO\\O R`RkR RTT\\T R`WjW R`ZjZ RTITS RYJYS RSJ\\J\\S R^G^J R`T`[ ReCeG RaIjIjL R`TjTj[ R^GlGlJ R`DbE RiDhG R\\V^Y RZVZZ R_N`R RVCRL RUF[FXL RTVS[ RWVWZ RaIaLiM_O',
	    'Po_EkE R`KcK RgKjK R_NcN RgNkN RTO\\O RTT\\T R_VkV R_ZkZ RTITS RWVWZ RYIYS RSK\\J\\S R^H^K R_R_[ ReEeP ReReZ R_RkRk[ R^HlHlK R\\V]Y RYVZZ RVCRL RUFZFXL RTVS[',
	    'Po_GjG R_MhM RTO\\O R^PlP RTT\\T RaWjW RaZjZ RTITS RWVWZ RYJYS RSK\\J\\S RbSb[ RcJcP R`TjTj[ R_DjDjJ R\\U]Y RZVZZ RgRiR RVCRL RVFZFXL RTVS[ R_D_JjKeQ^U',
	    'PoTO\\O RbPhP RTT\\T RTITS RYJYS RSK\\J\\S R_I_L RcMcP R_IkIkL RiMlP R_M^P RjFhI RcR^W R\\V\\X RYVZZ R`FaI RdFdJ RcKfL RaTeXl[ RVCRL RUFZFXL RTVS[ RbTiTeX_[ RWVWZ RjC^E',
	    'Po^EkE R_KbK RgKjK R^MbM RgMjM RTO[O R]PlP RTT[T Ri[l[ RTITS RWVWZ RXJXS RXVYY RSK[J[S R]H]K R^T^[ RcTc[ ReEeN RgTg[ R^TlTl[ R]HlHlK R[V[Y RVDRK RUFYFWM RTVR[ RcPbW',
	    'Po]FlF RfOjO RTO[O RTT[T R_WjW RTITS RVVVZ RXJXS RXVYZ RSK[J[S RdJdO RdCdF RgIgN R_RjQj[ RgGeI R[V\\Y RVCRK RUFYFWL R_I]O RkIgI RjIkO RSVR[ RaN_N R_P_U^Z R]IbHbIaO',
	    'Po^JaJ RTOZO RhPjP RTTZT R\\UlU RTITT RVVVZ RXIXT RXVXZ RSKZJ[T R^E^U RcOcU RcCcKfKfU RgEkEkU RgWl[ RaW][ R`D^F R[V[Y RVCRK RSVR[ RfGbG RjJgJ R`P^P RUFYFWM',
	    'Po]ElE R_MjM RTO[O R_OjO R_RkR RTT[T RTITS RWVWZ RXJXS RYVZY RSK[J[S R`I`S RaCaG ReIeR RgCgG R\\U\\X RVCRL RUFZFXK RTVS[ RaWfYl[ R^UjUgWcY\\[',
	    'Po^IjI R_LhL RTO\\O R^PlP RTT\\T RaVjV RaZjZ RTITS RWVWZ RYJYS RSK\\J\\S R^E^I RbSb[ RcIcO R`TjSj[ R^EjEkI R\\U]Y RZVZZ RVCRL RUFZFXL RTVS[ RjKdQ]T RbEbI RfEfI',
	    'Po]ElE R_HlH RaNkN RTO[O RaQkQ RTT[T RbXiX RTITS RVVVZ RXJXS RXVYY RSK[J[S R`T`[ RaKaQ RcFcJ ReKeW RhFhJ RaKkKkQ R[U[X RiUiY RVDRK RUFYFWL RSVS[ R`TlTl[h[ R]E]S\\Z',
	    'PoTO[O R]PjP RTT[T RTITS RVVVZ RXJXS RXVXZ RSK[J[S R]J]P R^R^Z RdRdZ R]JjJjP R]F`H RcFeI RhFkI RjChF R_C]F ReCcF R[V[Y RfShT ReVhW R`TbT R`VbW RVCRK RUFYFWL RgK`O R`LhN RSVR[ RaZ\\Z RhYbZ RjRkYl[',
	    'PoSOZO RbPfP R]SkS RSTZT RSVR[ RTHTS RVVV[ RXIXS RSKZJZS RbFbP RdQd[ RbFfFfP RdRkY RdS]Y RcCcG RZV[Y RXVXZ RkHhP RUFYFWL R_D\\J R`H\\P RUCRL R`J\\J RbKfKbK RjDiJgJ R`M`P\\O RkMkPgO',
	    'Po]FlF RTO[O R^RlR RTT[T RTITS RXIXS RYVYY RSK[J[S RbCbH RgIgN RhChH R^IkIkN RlXl[ R_UbW RgYb[ RjTdZ R[U\\X R^I^NiOjP ReThZk[ RVCRL RUFZFXL RTVS[ RWVWZ RbIcNfX R^S]Z',
	    'Po^FdF RfFlF R_KlK R`NeN RTO\\O RTT\\T RTITS RXIXS RYVYY RSK\\J\\S RaRaX RlXl[ RhVc[ R[V\\X RaFaI RjOfY RVCRK RUFZFXL RTVR[ R_C]I RfCdI RVVVZ RaReReV`W RgHgOiY RhFhH R_K^T][',
	    'Po^FkF R_IjI R]KlK RbNgN RTO\\O R`SiS RTT\\T R`UiU RTITS RXIXS RYVYZ RSK\\J\\S R`Q`Z RdFdK R`QiQiU RfKlQ RbL]Q R[V\\Y RaCaF RdUgYk[ RVCRK RUFZFXL RhCgF RjWfX RVVVZ RTVS[ ReZ][',
	    'Po\\ElE R]MbM RfMjM RTO[O R]OlO RTT[T R]WbW RfWjW R\\ZlZ RTITS RVVVZ RXJXS RXVXZ RSK[J[S R]H]M R]R]W R]HbHbM R]RbRbW RdEdZ RfHfM RfRfW RfHjHkM RfRjRkW R[V[Y RVCRK RUFYFWL RSVR[',
	    'PodEjE RaJhJ RdLjL RTO[O R`QjQ R`SjS RTT[T R^ZlZ RTITS RVVVZ RXJXS RXVYY RSK[J[S R`V`Z R`N`S RcVdZ RdCdL ReNeS RgVgZ R`NjNjS R`VkVkZ R^GlGlJ R[V[X RVCRK RUFZFWL RTVR[ R^G^T\\[',
	    'Po]FlF R^McM RfMkM RTO\\O R`QkQ RTT\\T R`TkT R`WkW R`ZlZ RTITS RWVWZ RYJYS RZVZY RSK\\J\\S R^I^M RaPa[ RbCbG R^IcIcM RfIfM RfOfZ RgCgG RfIkIkM RaO]S R]U]X RVCRL RUFZFXL RTVS[',
	    'Po]DcD ReDlD R^MlM RTO[O R^TkT RTT[T R`WdW Rf[l[ RTITS RVVVZ RXJXS RXVXZ RSK[J[S R^G^J R`T`[ R`H`J RbMbS R^GcGcJ RfGfJ RgMg[ RhHhJ RfGkGkJ R^PkPkT R[V[X RVCRK RUFYFWL RkVfX RSVR[ RdZ^[ R^M^U\\[',
	    'PoYEcE RaUkU RTW]W RaWkW RURUW RZRZW RSSYT]T]W R_G_K RbQbW RfRfW RUH_HiHiK R`SkSkW RjXk[ RWFWKhLkO RTYR[ RdO_S R`Y`[ RVMTO RWOSS RVQ[PYT R]X]Z RZYZ[ RWYW[ RdYd[ RgYg[ RbMbO RcQhPfT RZESH R\\M\\O',
	    'PoUQiQ RUWiW RR[l[ RVJVW R_K_W RTKiKiW R[DSM RYGdG_L',
	    'PoTP]P RTU]U Re[j[ RTITU RXJXU RSK]K]U RWCTI RVF\\FZJ RcR_Z R_FlFlSj[ ReFdLbV R^XYYTY',
	    'Po`NjN RTP]P RTU]U Ra[k[ RTITU RXJXU RRK]K]U RaNa[ R`EjEjO RlXk[ RVF[FYJ RVCTJ R\\XXYTY',
	    'Po_KlK RTP]P RTU]U Rf[l[ RTITU RXJXU RRK]K]U RgMg[ RlWl[ RgDjG RVCSJ RVF[FYJ RdQ`Z RdCdMcU R]XXYRY R[XTY',
	    'Po^GlG RTP\\P R_SkS RTU\\U Re[l[ RTITU RXJXU RRK\\K\\U R_K_S RfCf[ RkKkV RlXl[ RVF[FYJ RVCSJ R\\XRY',
	    'PoVKiK RVNiN RSQlQ RWVhV RWZhZ RWGWN RWSW[ R_H_N RWShSh[ RUIiHiN RYFcE`H RZCVHRI',
	    'Po^HlH RTP]P RTU]U Rd[i[ RTITU RXJXU RRK]K]U RdDeG RVCSJ RVF\\FYJ RbOjNjTi[ RcHcOaX RXYRY R\\XVY',
	    'PoTO]O R`PkP RTT]T R`[k[ RTITT RYJYT RSK]K]T R`E`[ RfEfP R`EkElR RlWk[ RVCSK RVF[FXK R\\WRY',
	    'PoTP\\P R`SfS RTU\\U Re[j[ RTITU RXJXU RRK\\K\\U R`K`U R`KfKfS R_EkEj[ RVCSJ RVF[FYJ R\\XXYRY R[XTY',
	    'Po^IlI RTP\\P RTU\\U RTITU RYJYU RRK\\K\\U RdTjZ RfDiG RgUb[ RVF[FYJ RbCbOeW RaNjNfW RVCSJ R`S][ RbI_W R\\XRY',
	    'Po_EkE RTP]P R^RlR RTU]U RTITU RYJYU RRK]K]U ReEe[ R`HaO RjHhO RVCSJ RVF[FXL RYXRY R\\XUY',
	    'PoeIlI RTO]O RTU]U R`YkY RTITU RXJXU RSK]J]U R`O`[ ReCeO R`OkOk[ RVF\\FYJ RVCTI R^XXYRY R[XUY',
	    'PoeFlF RSO]O R`QkQ RSU]U RTITU RXJXU RRK]K]U ReCeK R`KkKkS RVF[FYJ RVCSJ R`K`S^Z R]WRY',
	    'PoTO\\O R`QlQ RTU\\U R`ZlZ RTITU RXJXU RSK\\K\\U R`I`[ RfCfZ R`IlIl[ RWCTJ RVF[FYJ R\\XRY',
	    'PoaIlI ReNlN RTP]P ReTlT RTU]U RTITU RXJXU RRK]K]U ReHe[ RVF\\FYJ RVCSJ RcC^M R^XXYRY R[XUY',
	    'PocJlJ RTP\\P RTU\\U Re[i[ RTITU RYJYU RRK\\K\\U RaGa[ RiCi[ RcNfT RWCTJ RVF[FYJ RcC^M RbDaL R[XVYRY',
	    'PoTP^P R`QlQ RTU^U RTYZY R`YlY RTITU RYJYU RSK^J^U R`H`[ R`HlHl[ RWCSJ RVF\\FYJ RfCdH R^XXX',
	    'PoTP]P RaSgS RTU]U Ra[k[ RTITU RXKXU RRK]K]U RbKb[ RaKgKgS RlXk[ RVCSJ RVF[FYJ RbC_K RaGkGkTfU RXYRZ R]YVY',
	    'PoVTiT RVXiX RR[l[ RRIRM RWOWX R_O_X RUPiPiX RRIlIlM RUDXG RYMcL`O RiDdH R^D_G R[JVORQ',
	    'PoTP]P RTU]U RTITU RXJXU RRK]K]U RfCfN RdTj[ RVF[FYJ RbOfW R`HkHjL RVCSJ R`OkOhUdY`[ R\\WRY R`H`R^Z',
	    'Po`MkM R^MiM RTO\\O RTU\\U R_ZkZ RTITU RYJYU RSK\\J\\U R_Q_[ R_QkQk[ RaI]M RgHjJkN RVCSJ RVF[FXL RdCbI RXYRY R[XTY',
	    'Po_HkH R_NlN RTP\\P R_TkT RTV\\V R][l[ RTJTU RXKXU RRK\\K\\U ReCeN RePe[ RVCSJ RVF[FYK R\\YRZ',
	    'Po^HlH R_NkN RSP\\P RSU\\U R`ZjZ RTITU RXJXU RRK]K]U R`R`[ ReCeN R`RjRj[ RVF[FYJ RVCSJ R]XRY',
	    'Po_GiG R^LlL RTO\\O RbPkP RTU\\U Re[i[ RTITU RXJXU RRK\\K\\U RdCdL RVF[FYJ RVCSJ RkEjIcP]S RcOaUjUi[ R\\XRY',
	    'Po^GlG RTP\\P RaPjP RTU\\U RaUjU Rf[j[ RTITU RXJXU RRK\\K\\U RaKa[ R`MjLj[ RVCSJ RVF[FYJ RcE^O R\\XRY',
	    'Po^ElE RTP[P RTU[U Ri[k[ RTITU RXJXU RRK\\K\\U R_K_[ RdIdY RgKgY R_KkKk[ RVF[FYJ RWCSJ RdEcO R\\XRY',
	    'PobIiI RSO\\O RcThT RST\\T Rh[l[ RTITT RXJXT RRK\\J\\T R_E_[ RcMcV RcMhMhT R_ElEl[ RVCSJ RVF[FYJ R\\XXXSY',
	    'PoTP[P RTU[U Rh[l[ RTITU RXJXU RSK[K[U R^E^U R^EeEeU RhFhU RlDl[ RcWfZ R`W\\[ RWCTJ RVF[FXJ RaHaS`X R[XRY',
	    'Po_JlJ RSP\\P RSU\\U RbYkY RTITU RXJXU RRK\\K\\U RbPb[ RbPkPk[ RVCSJ RUF[FXJ R_E_T^Z RkD`E R[XSY',
	    'PoaMiM RSP[P R^QkQ RSU[U RTITU RWJWU RRK[K[U R^ZeZjXl[ RhUjX RfFlL ReDfG RcG]L RVCSJ RVFZFXJ RdCbI RcQ_Z R\\XRY RgYcY',
	    'Po]GlG RTP\\P RTU\\U RTITU RXJXU RRK\\K\\U RhQh[ RdCeG R`GaJdMkP RVCSJ RVF[FYJ RiGgKeM^P R_Z][ RaR`Y R\\XRY',
	    'Po^GlG RTP\\P RTU\\U RTITU RXJXU RRK\\K\\U RhJlN RdCeF RaK^O RaOdUiYl[ RVCSJ RVF[FXL RhOfUaY][ RXYRY R[XUY',
	    'Po_IlI R`OkO RTP]P R_UlU RTU]U RTITU RXJXU RRK]K]U RfIf[ RVF\\FZJ RaDbG RVCSJ RiDgI R]XXYRY R[XUY',
	    'PoTFjF RVIiI RRLlL R[PdP RWUhU RWXhX RS[k[ RXQXX R_R_X RURhRhX RYD[F ReDcF R_GZMRP R\\OUR',
	    'PoUHiH RSKlK RWUhU RWXhX RS[l[ RXQXX R`R`X RVRhRhX RXDYF R[OcO`R ReDdH RdMlP R_C]HXMRP R\\NUR',
	    'Po`IjI R_NjN RTP]P R^SlS RTU]U Re[i[ RTITU RXKXU RRK]K]U RiPi[ R_EjEjN R`UcX RVCSJ RVG[FYJ R]YXYTZ',
	    'Po^ElE R_MkM RTO\\O R_RkR RTU\\U RTITU RXJXU RRK\\K\\U R_I_R R_IkIkR RVCSJ RVF[FYJ RdVaY][ R_TcXgZl[ ReEeQdV R\\XRY',
	    'Po]ElE RTP[P RTU[U Ra[c[ Rh[k[ RTITU RWJWU RRK[K[U R^J^[ R^JcJc[ RfJf[ RfJkJk[ R_OaT RgPiT RWCSJ RVFZFXJ R[XRY',
	    'Po`FkF RaMkM RTPZP R`SlS RTUZU Rb[l[ RTITU RWJWU RRKZKZU RfIfY R]E^H R\\M_M_X[[ R^Xd[ RVCRK RUFYFWL RfCaM R^M\\M RZXRY',
	    'PocEkE RTP[P R^UkU RTV[V R\\[l[ RTJTV RXJXV RRK[K[V R^E^O RbDbQ RdRd[ RfKkQ RiKdP RdFhN RjFgN RVCSJ RVFZFWL RZZR[',
	    'Po`JkJ RSP\\P R`PkP R_UkU RSU\\U R^[l[ RTJTU RXKXU RRK\\K\\U R`E`P ReEe[ R`EkEkP RVCSJ RVF[FYK R]XRZ',
	    'PodJlJ R^NbN RTP[P RTU[U R^WbW Re[j[ RTITU RXJXU RRK[K[U R^F^Y R^FbFcV RjCj[ RVFZFXJ RVCSJ ReOfT R[XRY',
	    'PoTP\\P R^RlR RTU\\U RTITU RXJXU RRK\\K\\U RhUlY R`U_Y RbE^J RaGeLlO RVCSJ RVF[FYJ RaFiGdL^O ReOe[a[ R\\XRY',
	    'Po`LfL RTP\\P ReQkQ RTU\\U Rb[e[ RTITU RXJXU RSK\\J\\U RfQf[ RiH`Q RVF[FYJ RdEcI`K RiTlY RiMlR RVCTJ RaT_Y RjD`E RgP_Q R\\XXYSY',
	    'PoVViV RVYiY RR[l[ RVRVY R_S_Y RcCcJ RTSiSiY RhElI RYQcPaR RWJSN R^FZI RRGVH RZPSS RUDXE ReLYN',
	    'Po`KjK RTP\\P R^PlP RTU\\U Rg[l[ RTITU RXJXU RRK\\K\\U R^G^J RhPh[ R^GlGlJ RlWl[ RdCeF RaW][ RVF[FYJ RVCSJ RbPaX R\\XRY',
	    'Po^IlI R_NjN RTP\\P RTU\\U RaZjZ RTITU RXJXU RRK\\K\\U RbQb[ R`EjEjN RaSjSk[ RVF[FYJ RVCSJ RcM_V RdHaS RdEbR R\\XRY',
	    'Po^JdJ R^OdO RTP[P RTU[U RiVlV RTITU RXJXU RRK[K[U R^E^Y R^EdEdO RgEg[ RgElElV RaRdY RVCSJ RVFZFWL RcW]X R\\XRY',
	    'PoTP\\P R_PlP RTU\\U R_UlU Ri[l[ RTITU RXJXU RRK\\K\\U R_K_[ RfKf[ R_KlKl[ RjGfH R_ElEhH RbGjK RWCTJ RVF[FYJ R\\XRY',
	    'Po^FkF R_IkI R]LlL RTP[P R_SjS RTU[U R_VjV Rf[j[ RTITU RXJXU RRK\\K\\U R_O_[ ReCeL R_OjOj[ RVCSJ RVF[FYJ R\\XRY',
	    'Po`GjG R^KkK RSP[P RaRiR RSU[U RSISU RXJXU RRK[K[U ReCeK RiMkP RcO^U RaM_O R`SeXl[ RVCRK RUFZFWL RhSdX][ R[XRY',
	    'Po^HlH RaLiL RTP\\P RaPiP R^TlT RTU\\U RTITU RXJXU RRK\\K\\U RaCaT RiCiT RhWl[ RbW^[ RVF[FYJ RVCSJ R\\XRY',
	    'Po^KdK RSO[O R^PdP RSU[U RTITU RWJWU RRK[K[U R^E^W RdEd[ RjRfZ RVCSJ RUFZFXJ ReV]W RgGgMiX R\\EfFlGiW R[XRY',
	    'Po^HcH RgHlH R_NcN RgNkN RTP\\P RgTlT R^TcT RTU\\U RTITU RXJXU RRK\\K\\U RcCc[ RgCg[ RVF[FYJ RVCSJ R\\XRY',
	    'Po_IjI R_MjM RTO\\O R_SdS RTU\\U Rf[l[ RTITU RXJXU RRK\\K\\U R_D_M R_O_[ RgOg[ R_DjDjM RlXl[ RVCSJ RVF[FYJ RkRfT RdZ^Z R\\XRY',
	    'PoaIkI RaMkM RTO\\O RTU\\U R`UlU R`ZlZ RTITU RYJYU RSK\\J\\U R`P`[ RaEaM RaEkEkM R`PlPl[ RWCTJ RVF[FYJ R^XRY',
	    'PoaKjK RTP\\P RTU\\U RbViV R_YlY RTITU RXJXU RRK\\K\\U R_E_[ RbPbV ReGeO RbPiPiV R_ElEl[ RUGRJ RVF[FYJ RWCTI R\\XRY',
	    'Po_KdK RfKkK RTO\\O R_PkP RTU\\U Rg[l[ RTITU RXJXU RRK\\K\\U R`F`P RgPg[ RfEkEkP RlXl[ RaW^[ RVCSJ RVF[FYJ RdD_F RcQaY R\\XRY',
	    'PoTP[P RTU[U Ra[h[ RTITU RXJXU RSK[K[U RbTb[ RhYh[ RdReW RdIeL RdDlL RdCcG^L RiUkZ R_NjNgS RVCSJ RVFZFXL R^U^Z R\\XRY',
	    'PobIjI RaMjM RTO\\O RTU\\U RbViV Rh[l[ RTITU RXJXU RRK\\K\\U RbQbX RfFfM RbQiQiV R_DlDl[ RVCSJ RVF[FYJ R\\XRY R_D_S^Z',
	    'Po^GlG RSP\\P R`PjP RSU\\U Rb[e[ RTITU RXJXU RRK\\K\\U R`K`P RePe[ R`KjKjP RVF[FYJ RiTlY RdCeG RVCSJ RaT^Y R\\XRY',
	    'Po^HfH R]LlL RTPZP RTUZU RTITU RWJWU RRKZK[U RgEjI RcK^P RVF[FYJ RbD^I ReLhOlP RWCSJ RdP^S RgS^W RjVcY][ RiGbH R[YTY',
	    'PoTP\\P R_TfT RTU\\U RTITU RYJYU RSK\\K\\U R_N_T RbIbY R_NfNfT RlXl[ RVCSJ RVF[FXL RfH^I R]EiEjUj[ R\\XRY RfVgZ]Z',
	    'PoTP\\P RTU\\U R_UlU R_ZlZ RTITU RXJXU RSK\\K\\U R_P_[ RePeZ R_PlPl[ R_IaM ReJfM RhIkL RkDiG ReDdH RWCTJ RVF[FYJ R`C^H R\\XRY',
	    'Po^FkF R]KlK RSP[P RSU[U RSISU RXJXU RRK[K[U R_O_W RaHaM RdCdH RhHhM R_OjOjW RfXl[ RVCRK RUFZFWL RdVbY][ R[XRY RdRdX',
	    'Po]GlG RdLiL R_OkO RTP\\P R^SlS RTU\\U RTITU RXJXU RSK\\K\\U R_D_O RdCdK ReOe[ RiDiK RfTlZ RdT]Z RVCSJ RVFZFXL R\\WRY',
	    'Po^ElE R`MjM RTP\\P RTU\\U R^UlU R^ZlZ RTITU RXJXU RRK\\K\\U R^P^[ R`H`M RePeZ R`HjHjM R^PlPl[ RVF[FYJ RVCSJ R[XTY',
	    'PoaNiN RTP[P R^RlR RTU[U RcZkZ R_ZgZ RTITU RXJXU RRK\\K\\U R^I^M ReCeI R^IlIlM R_DbG RiDhG RbW^Z RgUjWk[ RdRaX RVCSJ RVF[FXL R[YTZ',
	    'Po`HjH R`LjL RTO\\O R^PlP ReTkT RTU\\U RdZlZ RTITT RYJYT RSK\\K\\T R`D`L RePeZ R`DjDjL RVCRL RUFZFXL R`R^Z R\\XRY R`VcYjZ',
	    'Po_IjI R_NjN RTP\\P RTU\\U R\\[l[ RTJTU RYKYU RRK\\K\\U R_R_[ R_E_N RcRc[ RgRg[ R_EjEjN R_RkRk[ RVCSJ RVF[FXL R\\XRZ',
	    'Po_IjI R_NjN RTP\\P R]QlQ RTU\\U RTITU RXJXU RRK\\K\\U R_E_M R`Q`[ ReEeM R_EjEjM RfVk[ RkShV RVF[FYJ RdQgX RVCSJ ReY_Z R\\XRY',
	    'Po_JkJ R_OkO RTP[P RTU[U Ra[i[ RTITU RXJXU RRK[K[U R_E_O RbSb[ ReEeO R_EkEkO RiXi[ RdQfU RiSlY RVCSJ RVGZFWL R^S^Z RYYRZ R[YTZ',
	    'Po_JcJ RfJkJ R`NjN RTP\\P R^QlQ RTU\\U Rd[i[ RTITU RXJXU RRK\\K\\U R_E_J R_EcEdJ RfEfJ RfEkEkJ RVF[FYJ RWCSJ RbRaUjUi[ R\\XRY',
	    'Po\\KcK RSO[O RSU[U RTITU RXJXU RRK[K[U RaDa[ RaNcR RgSc[ RVCSK RUFZFWL RhChQkZ RgMeX RkIjP R`K]V RhKjX ReIdO RcD\\E R[XRY R`H^R',
	    'Po`GkG R`MjM RTO\\O R`PjP RTU\\U RTITU RXJXU RRK\\J\\U R`I`P R`IjIjP RcQ^V RVF[FYJ RaTdXl[ RVCSJ RaC_I RaTjTfW^[ RYYRY R\\XUY',
	    'Po`JkJ R`NkN RSP\\P R_RkR RSU\\U R`VkV R^[l[ RTITU RYKYU RRK\\K\\U R`F`N ReRe[ R`FkFkN RZHXL RVCSK RUF[FYK RdCdF R\\YYYTZ',
	    'Po_JjJ R_OjO RTP\\P RTU\\U Ra[d[ RTITU RXJXU RRK\\K\\U R_F_O RdOd[ R_FjFjO RfUlZ RbU]Z RjQgU RVF[FYJ RdPhW RVCSJ RdCdF R^RcR`W R\\XRY',
	    'PoeFkF RSP[P RbQlQ RSU[U Rf[i[ RTITU RRKYJXU R[J[U R`C`[ RiMi[ RdSeW R`Q\\U RkHeM RfDbH RVCRK RUFYFWM ReIgLbN R]H]M R[XRY',
	    'Po_KlK RSP[P RSU[U R`UlU RTITU RXJXU RRK[K[U R_F`R`[ RdOdZ RhOhZ R_FlFlK ReCfF RVCRK RUFZFWL R`OlOl[j[ R[XRY R_N]Z',
	    'Po^EkE RTO\\O RTU\\U R_UkU R^ZkZ RTITU RXJXU RRK\\K\\U R_P_U ReMeZ R_PkPkU RhWlZ RdFfH R`FdKkM RVCSJ RVF[FYJ RjFfK^N R\\XRY',
	    'PoSE^E RaGlG RTG]G RRJ^J RXN\\N RYQcQ RVVhV RVYhY RR[l[ RWRWY RXCXI R_R_Y RUShShY RWKSO RcD_H RVL]L\\N RbHfMlO R[PSS RiHeL_N',
	    'Po_FgF R^JlJ RTO\\O RbPkP RTU\\U R`VjV R`YjY RTITU RXJXU RRK\\K\\U R`R`[ RbLbO RcDcI R`RjRj[ RVCSJ RVF[FYJ RkDfJ]N RjLaM R\\XRY',
	    'Po_HkH R_LkL RTP[P RTU[U Ra[d[ Rh[l[ RTITU RXJXU RSK[K[U R_E_L R^OdOd[ R_EkEkL RfOlOl[ R^QaT RfRiT RWCTJ RVF[FXL RdU^W RlUhWfX RXYRY R\\XVY',
	    'Po^JkJ RSP\\P RSU\\U RTITU RXJXU RRK\\K\\U R^E^J RcEcJ ReJe[ RgEgJ R^EkEkJ RgTlX RgMkQ RbM^Q RbT]X RVCSJ RVF[FYJ R\\XRY',
	    'Po`OkO RTP\\P R^RlR RTU\\U R_ZkZ RTITU RXJXU RRK\\K\\U R_U_Z ReNeZ RkUk[ RaM^P R_H`K RdGdJ RjGhK RWCSJ RVF[FYJ R\\XRY RjD`E',
	    'Po_FlF RTO\\O R^PlP RTU\\U Re[i[ RTITU RXJXU RRK\\K\\U R_K_N ReMfP R_KkKlN RdCeF RbW^[ RVF[FYJ RbGbJ RVCSJ RhGgK RcTjTi[ RcPaX R\\XRY',
	    'Po]HkH R]OlO RTO[O R]SjS RTU[U RTITU RXJXU RSJ[J[U RaHa[ RfGf[ R^KjKjS RgUlZ R_DbF RhDfI R_V[Z RVCSJ RVFZFXL RaR_X R[WRY',
	    'Po\\FeF RSOZO R]RdR RSUZU R\\VdV Rg[k[ RTITU RXJXU RRK[K[U R]M]R R^C^JbJa[ RcCcJ R]MdMdR RVCRK RUFYFWM R[XRY ReIlIlVk[ RhChOfW',
	    'Po^ElE R_MkM RTP\\P R_PkP R^TlT RTU\\U Ra[e[ RTITU RXJXU RRK\\K\\U R_H_M RcEcM ReTe[ RgEgM R_HkHkM RiVlZ R`V]Z RVCSJ RVF[FYJ R\\XRY',
	    'Po_ElE R`KcK RgKjK R_NcN RgNkN RTO\\O RTT\\T R`VkV R_ZkZ RTITT RXJXT RSK\\J\\T R^H^K ReEeN R_RkRk[ R^HlHlK RVF[FYJ RWCTI R\\WRY',
	    'PobGkG RVVhV RVYhY RR[k[ RSHSO RVSVX RXCXN R[J[N RSH^H^N R_S_X RVShShX RTDUF R]D[F RcIgMlO RVJUN RcCaI RZPRS RhHeL`N RYQcQ^S',
	    'Po_GjG R_JjJ RSP[P R^QkQ RSU[U RSISU RXJXU RRK[K[U R^M^Q R_D_J R_DjDjJ R^MkMkQ R_TcXj[ RUCRK RUFZFWL RbMbQ RfMgQ R^TjTgXbY][ R[XRY',
	    'PoUE_E RbGlG RRK_K RTO_O RXRcR RUViV RUYiY RR[l[ RVRVY R_S_Y RSSiSiY RYHZI RXLZM RUDRG RbFdJgMlO RcCaI R[PSS RiFhJdM`O RUGTO RUG^G]O',
	    'Po^FlF R`LlL RaNjN RTP[P R`TkT RTU[U R`WkW RTITU RXJXU RRK[K[U R`Q`[ ReEe[ RaIjIjN RVCSJ RVFZFWL R`QkQk[h[ R^F^S\\[ R[XTY',
	    'Po\\JdJ ReJlJ R_NdN RSPZP R^ReR RSUZU RSISU RRJXJWU RZJZU R\\EdEdJ RcTeX RfNgS RVCSK RUFYFWL R_T]Y RbRa[_[ RjCj[f[ RZXRY R\\E\\S[Z',
	    'PoTP\\P RTU\\U R`UkU R`ZkZ RTITU RXJXU RSK\\K\\U R`Q`[ R^DdDdM RdNeQ R`QkQk[ RfDlDlM R_FaI RgFiI RWCTI RVF[FYJ RdJ^L RlIfL R\\XRY',
	    'Po]ElE R^KeK RTPZP R_TdT RTUZU RTITU RWJWU RRKZKZU R_N_T RbKbX RdNdT R_G`J RVCRK RUFYFWK RdGcK RgLkLjOiRkZ RgGfP RZXRY R]E]U[[ RiNgX',
	    'Po^GkG R_JkJ R^MlM RTP[P R]RlR RTU[U R_ZkZ RTITU RXJXU RRK[K[U R_U_[ RdGdR R_UkUk[ RaCbF RhCgG R`N`Q RiNhR RVCSJ RVFZFWL RZXSY',
	    'Po]IlI RTPZP ReQlQ RTUZU RdWlW RTITU RWJWU RRKZKZU RdCdN ReSdW RiOi[ R^DaF RjDgG R^UaV\\[ RdHgKlM RVCRK RUFYFWK R_O\\U R^RcR`X RdHaL]M RZXRY',
	    'Po]GlG RfMkM R^OkO RTP[P R^RkR RTU[U R]UlU Rd[g[ RTITU RXJXU RSJ[J[U R^J^R RgFgM RhRh[ R^JkJkR RaWcY R`DbE RcGcL`N RVCSJ RUFZFWL RhCfH R[XRY',
	    'Po]EkE R_MjM R_OjO RSO[O R_RkR RSU[U RTITU RWJWU RRK[K[U R_I_S RaCaG ReIeR RgCgG RVCSJ RVF[FXJ RaWfYl[ R^UjUgWcY\\[ R[XRY',
	    'Po^GlG RaJeJ RTP[P RaReR RTU[U RTJTU RXJXU RRK[K[U RaMaR RaMeMeR RbUa[iZi[ RcTfW RiDjF RiOeS RiVkZ RlQjSkT RkJgR RVCSJ RVGZFXL R^V][ RgCgIhR R[YRZ R^G^O]T',
	    'Po_IkI R_MkM R^PlP RTP\\P RTU\\U R`VjV R^[l[ RTITU RXKXU RRK\\K\\U R_F_M R`R`V RcCcM RgCgM R`RjRjV R_FkFkM RbXb[ RhXg[ RWCSJ RVF[FYJ R\\XRZ',
	    'Po]FlF RaMhM R^PkP RTP[P R_UjU RTV[V R_XjX R][l[ RTJTV RXJXV RRK[K[V R^I^O R_R_X RaKaN RaKhKhN R_RjRjX R^IkIkO RcDeF RVCSK RVGZFWL R\\YRZ',
	    'Po_GdG RgGlG RTP[P R`SiS RTU[U R`ZkZ RTITU RXJXU RSK[K[U R^K^O R`N`[ RaFbI R`NiNiR R`UkUk[ R^KkKkO RdIfK RVCRL RUFZFXK R`C]I RgCfI R\\WRY',
	    'PoVIhI RVMhM RVPlP Re[i[ RVFhFhM RcVfX R_C]E RZVZZ R_V_Y RUVS[ RVFVSkSi[',
	    'PoTI\\I RTL\\L RTO^O Ra[k[ RX[\\[ RVVVY RTF]F]L RZU[V RWCWF RXUXX RhH`Y R`FjFdR RlVk[ RSVRZ RTFTR^R\\[',
	    'PoVHhH RVKhK RVMlM Rd[l[ RYTdTeZ RVFhFhJ RlWl[ R_Q`S RdQeR R]C\\F RTQST RZQZS RXYR[ RYTXY RVFVOkOjTgT',
	    'PoTI^I RTM^M RTOaO R[[_[ Rf[j[ RWUWY RTF^F^L R\\T^V RXCXF RZUZX RSVRZ RaJlJlUk[ RTFTR`S_[ ReCeOcW',
	    'Po_IjI R_MjM R_PlP Re[j[ RaVaY R_FjFjL RgUiW RdUeX RdCdF R]R]U\\Z RRIZI[U]UZU R_F_RlRj[ RVCVOTW',
	    'Po[IiI R[MiM R[PlP Re[i[ RRDR[ R]U]Y RbUbX R[FiFhL RRJVN R`C`F RfUfW RYUWY R[F[SkSj[',
	    'PoUElE RZKhK RZOhO RZQlQ Re[i[ RZHhHhN ReVgX RaWbZ R`F`H R\\W^Z RXWW[ RZHZTkTi[ RUEUQSZ',
	    'PoVHhH RVKhK RVNlN Rd[l[ RYSdSeZ RVFhFhK RlXl[ R]C\\F RXYR[ RVFVQkQiVfV RXSXX',
	    'Po_IiI R_MiM R_PlP RVZZZ Rf[j[ RcVcY R_FiFiL RhUhW RcCcF ReUfX R_U^Z R_F_RkSj[ RWFWMUW RSF\\F\\RZZ',
	    'Po^IiI R^MiM R^PlP Re[j[ R`VaY R^FiFiL RfUhW RcCdF RWTRX RVLZW RTJWP RdUeY RZLUW R]V[Z RSG[GWT R^F^RkSj[',
	    'PoYKdK RYNdN RYQfQ RbZeZ R\\W\\Z R_W_Y RYHdHdN RbVbX RlWk[l[ RXVWZ RUEhEhTi[ RUEURSZ RYHYTeTeZ',
	    'Po_IjI R_MjM R_PlP Re[j[ RaVaY R_FjFjL RfUiW RdCdF RdUeX RWOSX R^U\\Z RYL\\V RYHUV RSFYF[T R_F_SkSj[',
	    'PoSE\\E R_IiI R_MiM RRN\\N R_PlP Rf[j[ RWEW[ RcVcY R_FiFiL RhUiW RcCdF ReUfX R_U^Z R_F_RkSj[',
	    'Po_IjI R_MjM RTN\\N R_PlP Re[j[ RTF\\F\\N RaVaY R_FjFjL RgUiW RdCdF RdUeX R^U\\Z R_F_SkSj[ RTFTTSZ',
	    'PoXHfH RYMdM RYPdP RYRfR R[W[Z RYJdJdP RaWcX RXWWZ R^W_Y R^H^J Re[a[ RUEiEiTj[ RlWl[ RYJYUfUe[ RUEUPTY',
	    'Po]IiI R]MiM R]PlP RRSYS Re[j[ RRFRV RRFYFYS R_V_Y R]FiFiL RfUhW RbUdX RbCbF R[VYZ R]F]SkSj[',
	    'Po`IjI R`MjM RRNZN R`PlP Rf[j[ RVEV[ RRE\\E]X RcVcY R`FjFjM RhUiW RdCdF RfUfX R`V`Z R`F`SkSj[',
	    'PoRGkG RWMeM RWPeP RWSlS Rd[h[ RZXZ[ RWKfJeP RlIlK RdWeY R_X`Z RUXR[ R\\H\\J R_EcIeKkK RWJWVjVh[ RdDhD',
	    'PoRH^H R`IjI R`MjM R`PlP Rf[j[ RXCXM RbVbY R`FjFjL RTP\\X RgUiW RZTSZ RdCdF ReUfX RSN\\NXV R_U^Z R`F`SkSj[',
	    'PoVIjI RYOhO RYQhQ RYTlT R]X][ RYLhLhQ RVFjFjI RfXfZ R_C`F RXXW[ RaXa[ Rj[g[ RYLYVkVj[ RVFUPTY',
	    'Po`IjI RUL\\L R`MjM R`PlP Rf[j[ RbVbY R`FjFjL RgUiW R[G_K RVHRL RdCdF RYC[I ReUfX RXCUJ R_U^Z RSQ\\QY[ R`F`SkSj[',
	    'PoSI\\I R`IjI R`MjM R`PlP RRP]P Re[j[ RbVbY R`FjFjL RXCXRZT[W RgUiW RdCdF ReUfX RXRTY R_U^Z R`F`RkSj[ RXLVV',
	    'PoaIjI RaMjM RaOlO RWZ[Z RTM\\M\\Y RdUdY RaFjFjL RgUiW RdCeF RZD^L RfUfX RVDRM RaU`Z Rj[f[ RWMUX RaFaRkSj[',
	    'Po_IiI R_MiM R_PlP Re[j[ RaVaZ R_FiFiM RgUhX RcCcF RVVRZ RWO[X RUNWQ RVDWH RdVeY RYOUX R]V[Z RZJWV R_F_SkSj[ RRH[HYQ',
	    'PoSE^E R`IjI R`MjM RRN^N R`OlO Rf[j[ R[E[[ RcUcY R`FjFjL RdCdF RfUgX RiUiW R`U_Z R`F`RlSj[ RVEVQUX',
	    'PoRE^E R`IjI R`LjL R`OlO RZW]W Rg[k[ RSJSW RXEX[ RSJ]J]W R`U_Z RcUcY R`FjFjL RgTiV RdCdF ReUfX R`F`RlRk[',
	    'PoRF[F R^IhI R^MiM RSNZN R^PlP Re[j[ RWFWW R`VaY R^FiFiL RgUiW RcCcF RdUeY R]V[Z R[VRW R^F^RlSj[',
	    'PoRE]E R_IjI R_LjL R_OlO Re[j[ RXFX[ RbVbY R_FjFjL RgUiW RdCdF RZL\\R RdUeX RWISS R^U]Z RXEUP R_F_RkRj[',
	    'Po`IiI R`MiM RSM^M R`PlP Rf[j[ RbVbY R`FiFjL RYEYR[T]V RhUiW RWVR[ RdCdF ReUfX RXRUY R_U^Z R]DSF R`F`RkSj[ RYLWV',
	    'Po`IjI R`LjL RRO^O R`OlO Re[j[ RSH\\H\\O RbUbY R`FjFjL RWCXS\\W RgUiV RdCdF ReUeX RWRTZ R_U^Z R`F`RkRj[ RWMVW',
	    'PoTI\\I RTL\\L RTO^PlP RX[\\[ RVVVY RTF\\F\\L R_HjHjP RgWl[ RZU[V RbV^[ RWCWF RXUXX RdCeRiY RdOfT RSVRZ R[ObP RTFTR]R\\[ RdMcV',
	    'PoRE\\E R`IjI R`MjM R`PlP RYS\\S RRY]Y RRERY RYEYS R]P\\T RdUdY R`FjFjL RhTiV RdCeF RfUgX R`U`Z Rj[f[ R`F`RkSj[ RVEVLTS',
	    'Po`IjI R`MjM RRO]O R`PlP RX[[[ Re[j[ RTE[E[Z RcVcY R`FjFjL RgUiW RdCdF RWHXM ReUfX R_U]Z R`F`RlSj[ RTETSSZ',
	    'Po`IjI R`MjM R`PlP Re[j[ RSHSL RXCXNXX RSH]H]L RbVbY R`FjFjL RgUiW RdCdF ReUfX R_U^Z R]UXW R`F`SlSj[ RXJVW',
	    'Po_IjI R_MjM RTM\\M R_PlP RRS\\S Re[j[ RTCTL RWNW[ R\\J\\M RbVbY R_FjFjL RgUiW RdCdF ReUfX R_U]Z R[FSH R_F_SlSj[',
	    'PoUElE R_LjL R_OjO R_RlR Rg[j[ RZJZ[ RaWaZ R_IjIjO RgVhX RZKVO RcFcI RdWdZ R[FXN R^W][ R_I_TkTj[ RUEUQSZ',
	    'PoSE]E R`IjI R`LjL RSN^N R`OlO Re[j[ RW[[[ R[E[[ RbUbY R`FjFjL RgUiV RdCdF RXRRX ReUeX R[LXS R_U^Z RTGSO R`F`RkSj[',
	    'PoRE]E R`IiI R`MiM RXM]M R`PlP Rf[j[ RTKTX RXEXW RbVcY R`FjFjM RhUiW RdCdF ReUfY R_U]Z R]URX R`F`RkRj[',
	    'PoRH^H R`IjI R`MjM R`PlP Rf[j[ RcVcY R`FjFjL RgUiW RdCdF ReUfX R_U^Z R[[W[ R`F`SkSj[ RXCWHVOTY RVN\\N\\U[[',
	    'Po^EdE RTE^E RRIlI RWOgO RWRgR RWTlT RVMhMgR RdXeY R_X`Z RVXS[ R[XZ[ RcJkN R^GZKTN RXLXVjVi[ Re[i[e[ RhDaD',
	    'PoSFlF RWOhO RWRhR RWTlT Rf[i[ RWMhMhR RWMWVjVi[ RdXeY R_XaZ R^C`E RVXS[ R_K]L RZXZ[ RWF]HfK R\\HkL RfFaISK',
	    'PoRE\\E R_IjI R_MjM R_PlP RRW]W Re[j[ RRERY RaVaY R_FjFjL RTJ\\R RgUiW RcCdF RdUeX RZIUU R_V]Z R_F_SkSj[',
	    'PoTI\\I RTL\\L RTO\\O RX[\\[ RVUVY RXUYY RTF\\F\\L RaHa[ RWCWF R[UZX RiEjH RcD^N RSURZ RlXk[i[ RfCfNhY RTFTR]R\\[ RkKbL',
	    'PobJlJ RZJ^J RWPiP RWRiR RWTlT Rf[j[ RbEbJ RWMiMiR RbElElJ ReXgY R`XaZ RWXT[ RXGTK R[X[[ RWMWVkVj[ RYDVJ RSE_E^J',
	    'PoWPhP RWRhR RWTkT Re[i[ RWMhMhR RcXeY R_X`Z RVXS[ RZXZ[ RbEeIlL R`EkEgIaK RXEUH]J RWMWVjVi[ RSE^EZJRL',
	    'Po`IjI RUK\\K R`MjM R`PlP RZW]W Re[j[ RWPW[ RSP]P]W RbVbY R`FjFjL RgUiW RXC]G RWFRK RdCdF ReUfX RXCVH R_U^Z R`F`SkSj[',
	    'PoaIjI RaMjM RaPlP RW[\\[ RWIWZ RaU`Z RdUdY RaFjFjL RhTjV ReCeF RWURZ RfUgX RUDYE R[KVW Rj[g[ R]M_T RSMRT R]EYS RaFaRlSj[ R\\U\\Z',
	    'Po`IiI R`MiM R`PlP Rf[j[ RWHXL RbVbY R`FiFiL RhUiW RcCcF ReUfX R[OUUYY\\Y RTURY RRL\\LZP R_U^Z R\\DRF R`F`RkSj[',
	    'PoTI\\I RTL\\L RTO^O Rb[k[ RX[\\[ RVVVY RTF\\F\\L R_H_K RbLb[ ReCeH R_HkHkK RlWk[ RZU[V RWCWF RXUXX RkOaS RSVRZ RTFTR^R][',
	    'Po`IjI RSK]K R`MjM R`PlP RSP^P Rg[j[ RSDSZ RSD]D]J RcUcY ReUfX R`FjFjL RdCdF RhUhW R`U_Z R]W\\Y]Y RXYRZ RYKYPZX R`F`RlSj[',
	    'PoRI]I R`IjI R`MjM R`PlP Rf[j[ RWCWI RaVbY R`FjFjL RfUhW RdCdF RdUeX R_V]Z R[LYW R]VRX RTLUU R`F`SkSj[',
	    'Po`IjI RSJ^J R`MjM R`PlP RRQ_Q Rg[j[ R[L[[ RbUbY R`FjFjL RgTiV RZF_K RdCdF ReUfX RWCTJ R_U_Z RVLVSTZ R`F`SlSk[',
	    'PoUH\\H R_IjI R_MjM RRO]O R_PlP Rf[j[ RaVaZ R_FjFjL RXGXR\\V RgUiW RVWR[ RdCdF RdUeY RVDRL RXRTY R^U\\Z R_F_SlSj[ RXMWV',
	    'PoTI]I RTM]M RTO^O RaSfS Re[j[ RY[\\[ RVVVY RTF]F]L RaKaU RaKfKfS R_EkEj[ RZU\\V RXCXF RXUYX RSUSZ RTFTR]S\\[',
	    'PofHiH RWOhO RWRhR RWTlT Rf[i[ RWMhMhR RaDiEiH RbDaKlJkK RdXfY R_XaZ RUXS[ RXCTH R_J]M RZXZ[ RUGZI RVF^FZJSM RWMWVjVi[',
	    'PoYF^F RTJ[J RWOgO RWRgR RWTlT Re[i[ RTETJ RZXZ[ RWMgMgR RbCaJkIjJ RdWeY R^X`Z RVXS[ RWMWVjVi[ RjEaG R^JXJ RYCYJRK',
	    'Po`IjI R`MjM R`PlP RUSYS RXZ\\Z Rf[j[ RULUU RULYLZS RbUcY R`FjFjL RgTiV RdCdF ReUfX RVCRL RUH]G^V_Z R`F`SkSj[ R]R\\Z',
	    'Po`IjI RTK\\K R`MjM RRN^N R`PlP RW[Z[ Rf[j[ RTETK RTE\\E\\K RbUbY R`FjFjL RgTiV RdCdF ReUfX R_U^Z R`F`SkSj[ RUNTT\\S[[',
	    'Po`IjI RTL^L R`MjM R`PlP RSZ[Z Rf[j[ RTFTV RcVcY R`FjFjL RgUiW RdCeF ReUfX R^T_V_Z RYURV R]DTF RZEZM[V R`F`SlSj[',
	    'Po_IjI RTL\\L R_MjM R_PlP RTS\\S Re[j[ RTETS RTE\\E\\S RbVbY R_FjFjL RgUiW RdCdF ReUeX R_U]Z R_F_RkSj[ R\\WRY',
	    'Po_IiI RTL[L R_MiM R_PlP RTQ[Q Rf[j[ RTFTY RaUbY ReUeX R_FiFiL RcCcF RhUhW R^U]Z RTF[F[WRY R_F_RkSj[',
	    'PobIjI RbMjM RbOlO RdUdY RbFjFjL RiUiW RfUgX RYI_I`V`Z RXKRW ReCfF Rk[g[ RUDSN RWRXVRV R^[[[ RWMRN RbFbRlSk[ R\\C[OYY R_R^[',
	    'PoRI]I R_IjI R_MjM R_PlP RSX\\X Re[j[ RSPSZ RWCWP RSP\\P\\X RbVbY R_FjFjL RgUiW RdCdF ReUeX R_U^Z R_F_SkSj[',
	    'PoTI\\I RTL\\L RTO]O RX[\\[ RVUVY RXUXX RZT[W RTF\\F\\L RXCXF RbGdLiQlR RcRhT RcC_K RbGjGgLbQ_R R`WjZ RSURY RTFTR]R\\[',
	    'PoTI\\I RTL\\L RTO^O RTF]F]L R`H`K R`HkHkK RWCXF R[U[X RdDfH RVVVY RSUSZ RcN`Z R\\[X[ RcLaX RgLiX RTFTR]S\\[',
	    'PoRIlI RWOgO RWRgR RWTlT Rf[i[ RWEWI RVMhMgR RdXeY R_X`Z RVXS[ R_K]M RZX[[ RWEgEhI RbJfLlM RXMXVjVi[ R_C]HZLRN',
	    'Po`IjI R`MjM R`PlP Re[j[ RSGSJ RSG]G]J RcUcY R`FjFjL RhTiW RdCdF RWCXG ReUfX R_U^Z RUL[L\\WZX R`F`RkSj[ RULUSTY',
	    'Po^IiI RRK[K R^LiL R^OlO RRR[R Re[j[ RRERS RWEW[ RRE[E[S RaVbY R^FiFiL RgUiW RcCcF RdUeX R]U[Z R^F^RkRj[',
	    'PoTI]I R_IiI R_MiM RRO^O R_PlP Rf[j[ RbVbZ R_FiFiM RYCXQZS\\W RhUiW RcCdF ReUfY RXRTZ R^U]Z RUESM R_F_RkRj[ RXLWV',
	    'Po`IjI R`MjM R`PlP Re[j[ RSGSK RXCXG RSG^G^K RbVbY R`FjFjL RgUiW RdCdF ReUfX R_U^Z RVOR[ RVLZL\\W R`F`SkSj[',
	    'Po^IiI R^MiM RRM[M R^PlP RRU[U Re[j[ RRERX RVEVU RRE[E[U R`VaY R^FiFiL RfUhW RcCcF RcUeX R]U[Z R^F^SkSj[',
	    'PoWNfN RWPfP RWSlS Rd[i[ RRHRK RWKfKgP RRHkHkK RcWeY R^X`Y RUDXF R]C^F RTWS[ RgDcH RYXYZ RWKWUjUi[',
	    'Po_IiI R_MiM RRMZM R_PlP RRYZY Rf[j[ RRGRZ RRGZGZM RRRZRZX RbVbY R_FiFiL RhUhW RcCbF RdUeY RVCTG R^U\\Z R_F_SkSj[',
	    'PoRG^G RaIjI RUL[L RaMjM RaPlP RRP^P RW[[[ RdUdY RaFjFjL RXF]L RhTiV RdCdF RXGRL RfUfX RXCUJ R`V_Z Rj[g[ RVPUT\\T[[ RaFaRkSj[',
	    'PoRE_E RaIjI RaMjM RaPlP Rg[j[ RSJS[ RWIW[ RZJZ[ RSJ^J^[ R`U`Z RdUdY RaFjFjL RiTiW ReCeF RfUgX RWEVN RaFaSlSj[',
	    'Po`IjI RRK]K R`MjM R`PlP RTY\\Y Rf[j[ RTQTZ RXEXQ RTQ\\Q\\Y RbUbY R`FjFjL RgTiV RdCdF ReUeX R_U^Z R\\DRE R`F`SkSj[',
	    'PoRG^G R_IiI R_MiM R_PlP Rf[j[ RVCVJ R[C[J RbVbY R_FiFiL RTN\\U RhUiW RcCdF RXTRZ ReUfX R\\LWV R_U^Z R_F_RkSj[',
	    'Po_IjI R_MjM RRM\\M R_PlP RRU[U RRZ[Z Rf[j[ RSCSM RRPR[ RRP[P[[ RbUbY R_FjFjL RgTiV R\\J[M RdCdF ReUfX R_U^Z R[FRG R_F_SlSj[',
	    'Po`IjI R`MjM RRN^N R`PlP Rf[j[ RSGSJ RXCXG RSG^G]J RbUcY R`FjFjL RgUiV RYUTZ RdCeF ReUfX RTT]X R`U^Z RWITT R[NYV R`F`SlSj[',
	    'PoXE_E RaIjI RXL`L RaMjM RaPlP RZ[][ RVLV[ R]L][ RdUdY RaFjFjL RhTjV RUERI RUMRP ReCeF RfUgX RVITO Rj[g[ R`U`Z RaFaSlSj[',
	    'PoaIjI RaMjM RaPlP Rg[j[ R[D[Y RcVcZ RaFjFjM R[N^R RhUiW RWORT ReCeF RSHUL RfUfY R^H\\L R`U_Z R_WYY RaFaSkSj[ RWDWRUW',
	    'PoRH]H R`IjI R`LjL RSN\\N R`OlO RTZ[Z Rf[j[ RTRT[ RXCXN RTR[R[[ RbVbY R`FjFjL RgUiW RdCdF ReUfX R`U_Z R`F`RkSj[',
	    'PoSDaD RfMjM RWPhP RWRhR RWTlT RdDdK RWNhNhR RkCkL RcXeY R_X`Z RVXT[ RZXZ[ RYDVH_G\\JUM RXFRI RVI[KRN RWNWVjVi[e[',
	    'PoTI\\I R_IiI R_MiM RRO]O R_PlP Rf[j[ RXCX[ RbVbY R_FiFiL RWP]U RcCcF ReUeY RhUhW RXMTV R_U]Z RUESL R_F_SlSj[',
	    'PoRI]I R_IiI R_MiM RRN]N R_PlP RRS]S Rf[j[ RbVbY R_FiFiL RhUhW RWWS[ RcCcF RTDUG ReUfX R\\DZH R_U]Z R_F_RkSj[ RXIXRWX',
	    'PoSG^G R`IjI R`MjM RTN^N R`PlP Rg[j[ RSJ^J^N RcUcY R`FjFjL RhTiV RWWR[ RdCeF RXCXS\\Z RfUfX RXRUY R`U_Z RSNSS^R]V[V R`F`SlSj[ RXNWW',
	    'PoXH^H R`IjI R`MjM R`PlP Re[j[ R[H[T RbUbY R`FjFjL RgUiW RdCeF ReUeX RSDWE RRKVM RVSSZ R_U]Z R_TXU R`F`SkSj[',
	    'PoTI\\I RTL\\L RTO^O RaZjZ RX[\\[ RVVVY RTF\\F\\L RaRa[ RaRjRj[ RZU[V RcP^R RhJaQ RWCWF RXUXX RbGdLhPlQ RcC`K RbGiFfM RSVRZ RTFTR]R\\[',
	    'PobKkK RWPhP RWRhR RWTlT RbEbJ RWMhMhR RbEkEkJ RdXfY R_XaZ RVXS[ R[X[[ RXDUH^J RSE^EZJRL RWMWVjVi[f[',
	    'Po`IjI RRJ]J R`MjM RTP]P R`PlP RRU^U Re[j[ RXLX[ RbVbY R`FjFjL RgUiW R[G]I RdCdF ReUfX RWCTJ RTMSS R_U^Z R`F`SkSj[',
	    'Po`IjI RUL\\L R`MjM R`PlP RUX\\X Re[j[ RUPUZ RUP\\P\\X RbVbY R`FjFjL RgUiW RYC]G RXCVGRK RdCdF ReUfX R_U^Z R`F`SkSj[',
	    'PoZHlH R\\LjL RXQhQ RXShS RXUlU Rf[i[ RXEXL R\\EcEcL RXOhOhS R`X`Z RVYT[ R[Y[[ RXOXWjWi[ RYCVFRH RiDbD',
	    'PoRH^H R`IjI R`MjM R`PlP Re[j[ RXCXG RbVbY R`FjFjL R[J]M RVQ\\W RgUiW RXUR[ RUJSN RdCdF ReUfX RZOWW R_U^Z R`F`SkSj[',
	    'PoaIjI RXJ`J RaMjM RaPlP Rf[j[ RVGV[ R\\C\\[ RcVcY RaFjFjL RhUiV R[K_S RfUfX RXDSN ReCeF R`V_Z R\\GYU RaFaSlSj[',
	    'Po`IjI R`MjM RVO^O RSP\\P R`PlP Rf[j[ RXQXW RbVbY R`FjFjL RgUiW RdCdF RWIXM ReUfX R_U^Z RXCVG\\G\\[Y[ R`F`SkSj[ RTGTUS[',
	    'PoRE^E R`IjI RTL]L R`MjM RRM[M R`PlP RSR]R Re[j[ RXNXX RbVbY R`FjFjL RZI]L RgUiW RdCdF ReUfX RWETL R_U^Z R]WRX R`F`SlSj[',
	    'Po`IjI R`MjM R`PlP RUY]Y Rg[j[ RURU[ RUR]R]Z RcUcY R`FjFjL RXK]Q RhTiV RdCdF RVGYM RfUfX RWCTJ RVG\\GZKUPRQ R`U_Z R`F`SkSj[',
	    'PofElE RZIcI ReLlL RZMcM RZPeP Rf[i[ RWKW[ R\\U\\Y RZFcFcM RiLi[ RaUbW RWCRH RXIVLRP R^C^F R^U_X RYUXZ RcZ`Z RZFZSdSc[',
	    'PoUG]G R`IjI R`MjM RTM]M R`PlP RRT^T Re[j[ RTOTT RYGY[ RbVbY R`FjFjL RgUiW RdCdF ReUfX RVDRL R_U^Z R`F`SkSj[',
	    'Po`IjI RUJ[J R`MjM R`PlP RUP[P Re[j[ RUEUV RRE]E\\[ RbVbY R`FjFjL RgUiW RdCdF ReUfX R_U^Z R`F`RkSj[ RYVRW R[VTW',
	    'PoRFlF RVPhP RVRhR RVTkT Rf[i[ R[F[K RbFbK RVNhNhR RdXfY R_X`Z R^CaE RgGkK RVXS[ RWGSK RZXZ[ RVNVVjVj[',
	    'PoRH^H R`IjI R`MjM R`PlP Rf[j[ R`U_Z RcUcY R`FjFjL R\\M^Q RTLVP RgTiV RVWR[ RdCdF RXCXS\\Z ReUfX RUJSR R\\JZQ RXMZW R`F`SkSj[ RXMWW',
	    'PoaIjI RTJ]J RaMjM RaOlO RWZ\\Z RWRWZ R\\W\\Z RSE]E]I RUJTO^N]O R`U`Z RcUcY RaFjFjL RXQZT RhTiW RdCdF R]S^X RfUgX RSSRY RaFaRkSj[f[',
	    'PoSI\\I RaLjL RSM\\M RSP]P R_QlQ RSS]S RRURZ RSFSR RUUVY RSF\\F\\L RZT[V ReDlK RdE_K RXUYX RiTkY RWCWF RaT_Y R[[Y[ ReLe[b[ R]S\\[',
	    'Po`IjI RUK[K R`MjM R`PlP RSP]P Rf[j[ RSGSJ RZPZY RSG^G^J RbVcY R`FjFjM RgUiW RXCYF RdCeF ReUfY R^WYY R_V^Z RVPUY R`F`SlSj[',
	    'Po`IiI R`MiM R`PlP RbVbZ R`FjFjM RhUiW RdCdF RXGYK RfUfY RTU\\Y RXMTT R]G\\L R_U^[ RRP]PZVXXR[ RTHTL Rj[g[ R`F`SlSk[ R]DRF',
	    'PoRG^G RaIjI RaMjM RaOlO RSP]P RSU]U RSKS[ RXCX[ RcUdY RaFjFjL RhUjV RZD]F ReCeF RfUgX RaU`Z RSK]K][Z[ RaFaRkSj[f[',
	    'PoRE^E R`IjI R`MjM R`PlP Re[j[ RXEXV RbVbY R`FjFjL RULVR RhUiW RdCdF R\\L^Q ReUfX R_U]Z R]URX R\\IZS R`F`RlSj[ RUIST',
	    'Po_IjI RSJ[J R_MjM R_PlP RRQ\\Q RRV\\V RX[[[ Re[j[ RRMR[ RSDSJ RSD[D[J RRM\\M\\[ RbVbY R_FjFjL RgUiW RdCdF RdUfX R^U]Z R_F_SkSj[',
	    'Po`IjI R`MjM R`PlP RUY\\Y RUQUZ RUQ\\Q\\Y RcUcY R`FjFjL RXJ^P RgTiV R[D^G RXJVNRR RdCdF ReUfX RVDTI Rj[g[ R_U^Z R`F`SkSj[',
	    'PoRG^G RaIjI RaMjM RaPlP Rf[j[ RXCXJ RXQX[ RSJSL^K^M RcUcY RaFjFjL RgUiV RdCdF RTN\\NXR RfUfX R`V_Z RU[X[U[ R[TRU RaFaSkSj[ R^SVT',
	    'PoTI\\I R^IkI RTL\\L RTO]O RbRjR RX[[[ RVUVY RTF\\F\\L RZU[V RhFlJ RbL_N RcQ^U RWCWF RXUXX RbTfYl[ RgIgNkMlN RcCaIaM RcNaS RiSeX^[ RSVRZ RTFTR]R\\[',
	    'PoTI^I RaIjI RaMjM RaPlP Re[j[ RcVcY RaFjFjL R[K^N RXT\\X RhUiW R[E^H RWESH RVKSN RWORT ReCeF ReUfX RVQ]Q[UWYSZ R`U_Z RaFaRkSj[',
	    'Po_IiI RSK\\K R_MiM R_PlP RSP\\P Re[j[ RSESY RSE\\E\\O RbVbY R_FiFiL RcCcF ReUfX RgUhW R_U^Z RZR[WRY R_F_RkSj[',
	    'PoaIjI RaMjM RSM_M RaPlP R\\Y^Y RVPVY RZOZ[ RcVcY RaFjFjL RVORS RfUfY RhUiX RTE^I ReCeF RXJTR^Q_W_Z Rj[g[ R]CZGSJ RaFaSlSj[',
	    'Po`IjI RRI^I R`MjM RSN\\N R`OlO RVY\\Y Re[j[ RVPV[ RSE\\E\\N RUR\\R\\Y RbVcY R`FjFjL RgUiW RdCdF ReUfX RWEWNRV R_U_Z RWHUS R`F`RlSj[',
	    'Po_IjI RSJ[J R_MjM RSO[O R_PlP RST[T Re[j[ RSDST RSD[D[T RbVbY R_FjFjL RZV[Y RgUiW RcCdF RdUeX RUVR[ R^U]Z R_F_SkSj[',
	    'PoRI_I RaIjI RaMjM RaPlP Rg[j[ RYEYO RUQ[Q[Y RcVcY RaFjFjL R\\L_M RYJ\\L R^W[Y RYIRO RdCdF ReUfY RhUiW R`U_Z RaFaSkSj[ RURTY R]DTE',
	    'PoRE^E RaIjI RaMjM RaOlO RRR]R RXRXY R`U_Z RcUcY RaFjFjL RdCdF RWKXP RRKTP R[L]P RfUfX RhTiW RTGRK RXGVK R\\G[K Rj[f[ RaFaSkSj[ R]XRZ',
	    'Po`IjI RTM]M R`MjM R`PlP Rf[j[ RXHX[ RSH]H]L RcVcY R`FjFjL RgUiW RdCeF RVTRX RUCVF ReUfX R\\C[F RXPVU R`U^Z R]W[W R`F`RlRj[ RSMSR^Q]W',
	    'PoaIjI RRK_K RaMjM RaPlP R]W]Z RcVcY RaFjFjL RhUiW R[D[VWY ReCeF R\\E^I RfUfX R^NYX RXPRS Rj[g[ R`U_Z RVEV[S[ RXDRF RZQ[Z RaFaSlSj[',
	    'PoRGlG RXI_I RUJ]J RRJ[J RVQgQ RVSgS RVUlU Rf[i[ RSEYDYM RUMXNgNgS RkKlM R_Y`Z RUYS[ RZYY[ RaDcIfKjM RcYeY ReDiD RVNVWjWi[ RhI_L R]CUD',
	    'Po\\EkE R]HjH R[KlK RXPhP RXRhR RXTlT Re[i[ RcEcK RXNhNhR RdXeY R_XaZ RVXS[ R_K^N RZXZ[ RVCYFRF RYGRJ RXNXVjVi[ RXLUL RYEYJ',
	    'PoTH]H R`IjI R`MjM RRM^M R`PlP RTX\\X Re[j[ RTQTZ RXCXP RTQ\\Q\\Y RbVbY R`FjFjL RgUiW RdCdF RdUeX R_V]Z RTDSK R`F`SkSj[',
	    'PoTFYF RaIjI RRI_I RaMjM RaPlP Rf[j[ RSNSZ RWKWY RdUdY RaFjFjL RhTjV ReCeF R]E^G RfUgX R^U^Z R`U`Z RYPVP RZXRY R\\D[S\\Y RaFaSlSk[',
	    'Po`IjI RUJ\\J R`MjM RUN\\N R`PlP Rf[j[ RUFUS RXCXG RbUbY R`FjFjL RgUiV RdCdF R]PVXRY ReUfX R_V^Z RUF\\F\\[X[ R`F`SkSj[ R\\QRS',
	    'PoSG^G R`IjI R`MjM R`PlP RTP]P Re[j[ RTJTP RXCX[ RTJ]J]P RbVbY R`FjFjL RXQ]V RgUiW RdCdF RVTRX ReUfX RXOUU R_U^Z R`F`SkSj[',
	    'Po`IjI R`MjM RTN]N R`PlP RTT]T RY[][ Rf[j[ RYJYS RSK]J][ RcVcY R`FjFjL RgUiW RdCeF ReUfX RVFZFXL R`U_Z R`F`SlSj[ RVCSKTUS[',
	    'PoTG^G RRI`I RfLjL RWPgP RWRgR RWUlU Re[i[ RYCYL RZYY[ RcDcJ RWNgNgR RjCjL R^X`Z R]D_G RUXS[ RUDRG RcYeY RWNWWjWi[',
	    'PoTI\\I R`IiI RTL\\L R_MlM RTO^O Ra[e[ RVUWY RTE\\E\\L ReMe[ R`EiEiM R_PbS R[T[W ReSkY RkOfT RXUYX RWCWE RSURZ ReU_X R\\[X[ RTETR]R\\Z',
	    'PoRI]I R`IjI R`MjM R`PlP RRP^P Re[j[ R[I[[ RbVbY R`FjFjL RgUiW RdCdF RUDVG ReUfX R[DYI R_U]Z R`F`RkSj[ RVIVRTX',
	    'Po`IjI RUJ[J R`MjM RSN]N R`PlP RTY\\Y Rf[j[ RTST[ RXJXS RTS\\S\\Z RcVcY R`FjFjL RhUiW RXD\\H RXCRI RdCdF ReUfX R`U^Z R`F`RkSj[',
	    'PobIjI R]K`K RbMjM RbPlP RU[X[ Rg[j[ RXEX[ RZEZ[ RZE`E`K RdUdY RbFjFjL RUJWL R\\Q`U RUPWR RfCfF RgUgX RjUjW RZN`O]Y RbUaZ RbFbSlSk[ RXETETTR[',
	    'PoSG_G RaIjI RaMjM RaPlP RTQ]Q RTT]T Rg[j[ RTLTV RYJYY RSM^M^T RcVcY RaFjFjL R[I_M RhUiV R[G]K RWIRM ReCdF RfUfX RYCUK R`V`[ R^XVY RaFaSlSk[',
	    'PoXG]G R`IjI R`MjM RSN]N R`PlP RSR]R RRV^V Re[j[ RSJSR RXCXJ RXRX[ RSJ]J]R RbVbY R`FjFjL RgUiW RdCdF RdUfX R_V]Z R`F`SkSj[',
	    'PobIjI RbMjM RbPlP R[R^R RU[X[ R\\[^[ Rg[j[ RTEXEX[ R[E^E_[ RdVdY RbFjFjL RiUjW RfCfF RgUgX RTKXK RaU`Z RTRXRTR R[K^KZK RbFbRlSk[ RTETTR[ R[EZUY[',
	    'Po^IiI RTJ[J R^MiM RRO\\O R^PlP Re[j[ RXJXY RaVaY R^FiFiL RfUhW RXD\\I RWDRJ RcCdF RTRTW RdUeY R[RZV R^V\\YRZ R^F^RkSj[',
	    'Po`IiI R`LjL R`OlO RSTWT RZT^T RSY^Y Rf[j[ RSOS[ RZO^O^Z R`U_Z RcUcY R`FjFjL RgUiV RdCdF ReUfX RWDTK RVG^F[K RWNRO R`F`RkRj[',
	    'Po`IjI RSK]K R`MjM RSO]O R`PlP RRV^V Rf[j[ RSFSO RZQZ[ RSF]F]O RcVcY R`FjFjL RgUiW RWCXFXOTT RdCeF ReUfX R`U^Z RXKWQ R`F`SlSj[',
	    'PoVI\\I RaIjI RVM\\M RaMjM RaOlO RVV[V RZ[^[ Rf[j[ RVQVX RYGYO RVQ[Q[V RTE^E^[ RcVcY RaFjFjL RgUiV ReCeF ReUfX R`V_Z RaFaRkSj[ RTETTR[',
	    'PoSG^G R`IjI RRK^K R`MjM R`OlO RSS]S RXCXK RXPXX RcUcY R`FjFjL R[M^P RgUiV RVMSP RdCdF ReUfX R_U^Z Rj[f[ R^XRY R`F`RkSj[',
	    'PoRG]G R`IjI R`MjM RRM^M R`PlP RTU\\U RTY\\Y Re[j[ RTQT[ RUDUM R[D[M RTQ\\Q\\Y RbVbY R`FjFjL RgUiW RdCdF ReUfX R_U^Z R`F`SkSj[',
	    'PoaIjI RTL]L RaMjM RaPlP RVS]S RVV]V RTEUSV[ RTH]H]L RbUcY RaFjFjL RhTiW ReCeF ReUfX Rj[g[ R_U_Z RVO]O][Z[ R\\DTE RaFaSlSj[ RTPRZ',
	    'Po`IjI R`MjM R`PlP RSP[P Re[j[ RWIWZ RcUcY R`FjFjL RWQ[U RhTiW RdCdF RTKUN ReUfX RUCSI RWOTW RTG]G^V_Z R\\[Y[ RZJYN R`F`RkSj[ R]S\\[',
	    'PoaIjI RaMjM RaPlP RTR]R Rg[j[ RTJTR RXKYSY[]Y][ RSK]K]R RcVcY RaFjFjL R[S]V RhUiW RVWR[ RdCdF RfUfY RVCSK RXRUY R`U`Z RaFaSkSj[ RVF[FXMWV',
	    'PoRE^E RaIjI RaMjM RaPlP RSKSRWRWY RZRZY RcVcY RaFjFjL RhUiV ReCeF RfUfX R`V_Z Rj[f[ RWEVKSK RZEZK^K^RZR RaFaSkSj[ R^XRZ',
	    'PobIjI RbMjM RbPlP R\\U^U RZ[_[ RSGSJ RYCYG R[L[Z RZL^L^U R_X_[ RSG_G_J RdUdY RbFjFjL RhTjV RgUgX RaVaZ RTQWSTZ ReCeF Rk[g[ RUJRS RXNUX RXMTM RbFbSlSk[',
	    'PoSG^G RaIjI RaMjM RaPlP RTWYW RY[][ RTQTX RTQYQYW RRN^N][ RcVcY RaFjFjL RZI]L RXI\\J RhUiV RXHTL RfUfX ReCeF Rj[f[ R`V`Z RaFaRkSj[ RXDXJ',
	    'PoRG^G RgLjL RXPhP RXTlT Re[i[ RYDYL RdXeY R_XaZ RWXT[ RXFTK RZXZ[ RXH]J RfFdK RXNXVjVi[ R[RWR RbReR RXNhNhRdR R_RbR R\\R_R RbC`GkFjL R`R]R RcR`R R\\CTD',
	    'PoRG_G RaIjI RaMjM RaPlP RVJV[ RYCYG RcVcY RaFjFjL RZN[P RhUiW R[VV[ RYR]Z RfUfX RVHRP R]PYY ReCeF Rj[g[ RYN[X RZHWQ RYL^L[V R`U_Z RaFaSlSj[',
	    'PoaIlI RTI\\I RTM\\M RaNlN RTP_P RaTlT RaZlZ RY[\\[ RVVVY RTF\\F]L RbGb[ RgGgZ RZT\\V RWCWF RXUYX RSVSZ RhDgI RcC`IaL RTFTR^R][',
	    'Po`IjI R`MjM RZN^N R`PlP RSR]R Rf[j[ RSGSJ RXCXG RXRXX R[G[N RSG^G^J RcVcY R`FjFjL RgUiW RdCeF ReUfX R`U^Z RVGUM R`F`SlSj[ R^WRY',
	    'PobIjI RbMjM RbPlP RaUaZ RdVdZ RbFjFjM RTGXL R[S_X RSSXX R[G`L RiUjW R^VYZ RWIRM R^IZN RfCfF RVVRZ RgUgY RSEXEUL RRPXQUY RZE`E]K RZP`Q\\X Rk[g[ RbFbSlSk[',
	    'PoTI]I R`IjI R`MjM RTM]M R`PlP RTSXS Rf[j[ RTDTM RTOTZ RTD]D]M RZOZ[^Z^[ RcVcY R`FjFjL RhUiW RdCeF ReUfX R^RYT R`U_Z RXYRZ R`F`RlSj[',
	    'Po`IkI RTI\\I RTL\\L R`MkM RTO]O R_TlT RVUVY RTF]F]L R`D`M RiOi[ R`DkDkM RZT[W RXUYX RcT`Z RXCXF RSVRZ RfO`P R\\[Y[ RTFTR]S\\[ RcPbX',
	    'Po_HlH RTI\\I RbLiL RTL\\L RTO^O RbPiP R_TlT RSVRZ RVUVY RTF\\F\\L RbCbT RiCiT RhWkZ RbW_[ RZT[W RXUYX RWCWF R\\[Y[ RTFTR]S\\[',
	    'PoRG^G RaIjI RaMjM RaOlO RTP\\P Rf[j[ RTJTP RXPXZ RTJ\\J\\P RcUcY RaFjFjL RhTiW RdCdF R[S\\W RWDYG ReUfX RTSSX R_U_Z RUZXZUZ RaFaRkSj[',
	    'PoaIjI RTJXJ RZJ]J RaMjM RaPlP RTP]P Rf[j[ RZPZY R^E^P RcVcY RaFjFjL RhUiW RdCeF ReUfX RTZRZ R^WZX RZE^E R`U_Z RYCUEVPUY RaFaRlSk[',
	    'PoTI\\I R`KdK RfKkK RTL\\L RTO^O R`PkP Rg[l[ RX[\\[ RVVVY RTF\\F\\L R`E`P RgPg[ RfEkEkP RlXl[ RZU[V RWCWF RXUXX R`Z^[ RdD`F RSVRZ RcQaY RTFTR]R\\[',
	    'PoSF^F RSI]I R`IjI RRM^M R`MjM R`PlP RTU]U RZ[][ Rg[j[ RTPT[ RXCXL RXPXU RTP]P][ RcVcY R`FjFjL RhUiW RdCeF ReUfX R`U_Z R`F`SlSj[',
	    'Po`IjI R`LjL R`OlO RST]T RSY]Y Rf[j[ RSNS[ RXNXY RSN]N]Z RcVcY R`FjFjL RWHYL RRHTM R[I^K RhTiW RYDWH R]D[H RdCdF ReUfY RTDRH R`U_Z R`F`RkRj[',
	    'PoSH^H R`IjI R`MjM R`PlP Rf[j[ RULVP RYCY[ R`U`Z RcUcY R`FjFjL R\\M^P RXQ^W RgTiV RdCeF RVTRX ReUfX RXOVV RUJSR R\\JZP R`F`SkSj[',
	    'PoRF^F R`IjI R`MjM RSN]N R`PlP RSR]R Re[j[ RSJSQ RXCX[ RSJ]J]Q RbVbY R`FjFjL RWR\\W RgUiW RdCdF RVTRY ReUeX RXPUV R_U^Z R`F`SkSj[',
	    'PoRF^F RaIjI RaLjL RRM_M RaOlO RSP]P RSI]I]P RcUcY RaFjFjL RhTiW ReDcF RfUfX RXCXPUU R`U_Z RTW]Y RRS]TYYRZ Rj[g[ RXMWS RaFaRlRj[',
	    'PoRF^F R`IjI R`MjM RTM\\M R`PlP RSP]P RT[X[ Re[j[ RTITM RXSX[ RXCXG RTI\\I\\M RbVbY R`FjFjL RgUiW RdCdF ReUfX R_U^Z R\\QXS R`F`SkSj[ RZUSU R\\UUU',
	    'PoTF^F RaIjI RaMjM RUN_N RaPlP RVQ^Q Rg[j[ RYCYF RVJ^J^Q RcUcY RaFjFjL RiTiW ReCeF RYHYS\\X RfUfX RYTWZ R`V_Z RaFaSlSj[ RTFTSRZ RYPXX',
	    'PoaIjI RTK^K RaMjM RTO^O RaPlP RVY]Y Rf[j[ RVSV[ RZKZS RVS]S]Z RTE^E^J R`U`Z RcVcY RaFjFjL RdCdF ReUfX RhUiW RaFaRkSj[ RTETRRZ',
	    'PoaIjI RTJ_J RaMjM RaOlO RWR^R Rf[j[ RVUVZ RWMWR R[K[Z R^M^R RTE_E_J RdUdY RaFjFjL RhTjV ReCeF RfUfX R`U`Z RaFaRlSj[ RTETSR[ R^T^ZXZ',
	    'PoTI]I RaIjI RaMjM RTM]M RaPlP Rf[j[ RTDTM RTD]D]M RcVcY RaFjFjL RhUiW RdCeF RVTSX ReUfX RVMSS RXPUW R[RWZ R`U_Z R\\ZYZ RaFaSlSk[ RUQ^Q\\Z',
	    'PoaIjI RTL]L RaMjM RaPlP RRP^P Rf[j[ RSGSJ RXDXG RSG^G^J RcUcY RaFjFjL RhUiV RdCeF R\\S]W RfUfX RUSRX R`U_Z RYPX[V[ RaFaSkSj[',
	    'Po`IjI R`MjM RSO]O R`PlP RRS^S Rf[j[ RWGXK RcVcY R`FjFjL RXLXU\\Y RhUjW RWWR[ RdCdF RfUfX R]G[K RXSUY R_U_Z RSHTK R`F`RlRk[ RXOWW R]DSE',
	    'PoXI_I RcIkI RcMkM RcPlP RYZ`Z R\\D\\X RbUbZ ReUeY RcFkFkL RfCfF RRMUMUWRZ RSDUH R[I_Q R^E_G RgUgX RiUiW RWX[Z R\\HXU Rj[g[ RcFcRkSj[',
	    'PoUOhO RUQhQ RUSlS Rf[j[ RYXY[ RULhLhQ RdXeY RUXR[ RjDhF RUESG R]E[F RaE`F R_X_Z RXG]I ReGkJ RULUVkVj[ ReCdH`J RYCWISJ',
	    'PoSGZG RcIjI RcMjM RcPlP RSQZQ RcSlS Rg[j[ RSKSQ RWCW[ RSKZKZQ RcFcS ReUeY RcFjFjL RVRZV RgUhX RWPSY RfCfF R`[][ RkSk[ R^C]P\\Y R[JaJaW`[',
	    'PoRH_H RaIjI RaMjM RaPlP RSS]S RSY]Y Rf[j[ RSMS[ RVDVK RXMXY R[D[K RSM^M^Z RdUdY RaFjFjL RhTjV ReCeF RfUgX R`U`Z RaFaSlSj[',
	    'Po`IjI R`LjL RUOYO R`OlO RRS]S Rf[j[ RXQX[ RYHYO RcUcY R`FjFjL RWS\\W RgUiV RTE]EZG RVFYISN RRI]I\\M RdCdF RWTRX ReUfX R`U_Z R`F`RlRj[',
	    'PoSG^G R`IjI R`MjM RRM^M R`PlP RTU\\U RTZ\\Z Rg[j[ RTPT[ RUHUL RXCXG RTP\\P\\Z RcUcY R`FjFjL RgTiV RdCdF ReUfX R_U_Z R[H[L R`F`SlSk[',
	    'PoSG^G RWPgP RWRgR RWUlU Re[i[ RYDYL RZYZ[ RWNgNgR R_X`Z ReGgJlL RVXS[ RkEhH RWNWWjWi[ RXGSK RaE_H RdHaK RWG\\I RcYeY ReDdJ R\\CTD',
	    'PoTH]H R`IjI RTL]L R`LjL R`NlN RRO_O RXS^S R\\[l[ RTDTL RTD]D]L RcTcW R`FjFjL RUUXY RhSiT RdCdF RfTfV R`T_W RjXgX R`F`QkQjX RURSZ RYOYYb[',
	    'PobIjI RRJYJ R[K_K RbMjM RbPlP R[R_R RSXXX RSPSZ RVDTPXPXX RbUaZ ReUeY RbFjFjL RiTjV RfCfF RgUgX Rk[g[ R[E_E_[][ RbFbSlSk[ R[E[TZZ',
	    'PoRF_F RaIjI RaMjM RaPlP RRKRO RSPSX RUGVJ RXCXF RXNX[ RRK^K^O RcUcY RaFjFjL RhTjV ReCeF RfUgX R[G[K R`U_Z RSP]P]XZW Rj[g[ RaFaSlSj[',
	    'Po`IjI RSJWJ RYJ^J R`MjM RTM]M R`PlP RRQ_Q RW[[[ Rf[j[ RSESJ RWEWJ RYEYJ RYE^E^J RcVcY R`FjFjL RgUiW RdCeF ReUfX R`U^Z RSEWESE RVQUU\\T[[ R`F`SlSj[',
	    'PoSGWG RaIjI RaMjM RaPlP RSU]U RSY]Y Rf[j[ RSDSM RSPS[ RSP]P]Z RZDZM^L^M RcUcY RaFjFjL RhTiW RXNVP RdCdF RfUfX R^FYH R`U_Z RWLRM RaFaRkSj[',
	    'PoRF]F R_IiI R_MiM R_OlO RT[X[ RUDUH RXNXZ RZDZH RbUbY R_FjFjL RgUhW RSJ\\JXN RcCcF RVSRW ReUeX RULXPUU RRP]P[T R^U]Z Rj[f[ R_F_RkSj[',
	    'PoRE]E R`IjI R`MjM RSM\\M R`OlO RRU\\U RRZ\\Z Rf[j[ RRPR[ RSHSM RXPXZ RSH\\H\\M RRP\\P\\[ RcUcY R`FjFjL RhUiW RdCdF ReUfX R`U_Z R`F`RkSj[',
	    'PoaIjI RUI^I RaLjL RaOlO RVR^R RVV^V RVY^Y Rf[j[ RVNV[ RZEZN RVN^N^[ RdUdY RaFjFjL RfUgX RhUiW RaUaZ RdCeF RaFaRkRj[ RTFTTR[ R^DTE',
	    'Po`IjI R`MjM R`OlO RRR^R Rf[j[ RSGSI RSG]G]I RcVcY R`FjFjL R[J\\N R[O\\P RXS\\X RWCXF RhUiW RVWR[ RdCdF ReUfX RVJTN R`U_Z RXNVX R`F`RlSk[',
	    'PoRE_E RaIjI RUJ^J RaMjM RUM^M RaOlO RRZ_Z Rf[j[ RRERZ RUGUM RUG^G^M RaU`Z RdUdY RaFjFjL RhUiW RdCeF RfUgX RVS^W RXMVS RTQ]Q[TYVSW RaFaRkSj[',
	    'PoTH]H RaIjI RTL]L RaMjM RaPlP RTX\\X RZ[][ RTDTL RTQTY RTD]D]L RdUdY RaFjFjL RYS\\U RhTjV ReCeF RfUgX RVLTR RYQWV Rj[g[ RUP^P`W`Z RaFaSlSk[ R^S][',
	    'PoaIjI RSK^K RaLjL RSO_O RaOlO RcUcY RaFjFjL RYV\\Y RgUiV RVU[VWZ RTGUJ RYFYI RfUfX RWSS[ R]F[K RWS]SZW RdCdF R`U_Z Rj[f[ RWLUX RaFaRkRj[ R]DTE',
	    'PobIjI RTK^K RbMjM RbPlP RUT_T RTFUSU[ RYOYZ R\\O\\Z RTF^F^K RUO_O_[ RaUaZ ReUeY RbFjFjL RhTjV RfCfF RXCZF RgUgX Rg[j[g[ RbFbRlSk[ RTPRZ',
	    'PoaIjI RSI^I RaMjM RaPlP RSP]P RSS]S RRW^W Rf[j[ RSESH RSLSS RXGX[ RSL]L]S RSE^E^H RcVcY RaFjFjL RhUiW ReCeF ReUfX R`U_Z RaFaSkSj[',
	    'PoaIjI RTJ_J RaMjM RaPlP RVQ^Q RVU^U RVZ^Z RVMV[ RYEYJ RTE_E_J RVM^M`X`Z RdUdY RaFjFjL RhTiV ReCeF RfUgX Rj[g[ RaFaRlSj[ RTETTR[',
	    'PoRE^E RaGjG RaJjJ RTL]L RfMjM RVQgQ RVSgS RVUlU Re[i[ RTHTL RXCXH RTH]H]L RVNgNgS RaDjDjL R_Y`Z RUYR[ RZYZ[ RVNVWjWi[ RaE`L',
	    'PoSG^G R`IiI R`MiM R`OlO RRS^S Rf[j[ RTLTS RVCVJ R[C[J RTL]L]S RcUcY R`FiFiL RYJXT]Y RgUiV RWVS[ RdCdF ReUfX R`U^Z R`F`RkSj[ RXOWW',
	    'PoTI\\I RaIjI RRL_L RaMjM RaPlP R[R[Z RcVdZ RaFjFjM RYQ^V RiUjW ReCeF RgUgY RWCTI RaU`[ RXNRP RXQRT R_PZR RXNZTVWRY RVF]F[L RZ[V[ RaFaSlSj[g[',
	    'PoWPgP RWRgR RWUlU Re[i[ RZGZL RWNgNgR R^X`Z RUXS[ RWNWWjWi[ RRH_H]J RZYY[ RcDaFfJlL RbFjFfJ`L RTD^DYHVJRK',
	    'PoTH]H R`IjI RTL]L R`MjM R`PlP RRS^S Rf[j[ RTE]E]O RcUcY R`FjFjL RTETO\\P]R RXT]Y RhTiV RWVS[ RdCeF RfUfX RXSVY R`U_Z R`F`SlSj[ RXPWW',
	    'PoRF^F R`IjI R`LjL R`OlO RTR]R Re[j[ RTJTQ RVLVO RXCX[ RTJ]J]Q RbUbY R`FjFjL RWR\\W RgUiV RdCdF RVURY ReUeX RXPUV R[LZO R_U^Z R`F`RkSj[',
	    'PoVH\\H RaIjI RaMjM RUN]N RaPlP RTQ]Q RUZ]Z Rf[j[ RUKUUU[ RUK]K]Q RUT]T][ RcVcY RaFjFjL RgUiW RYC^H RYCRJ ReCeF ReUfX R`V_Z RaFaSkSj[ RTPSY',
	    'Po_FlF RSFZF RfMjM RWQhQ RWShS RWUlU Rf[i[ RWOhOhS RbIjIjM RTI\\J]L RWKYL R_Y`Z ReCfE RUYS[ RTKRM RZYZ[ RWOWWjWi[ RaL_M RYCSF R[ESI RcFaL',
	    'PoRG^G RaIjI RaMjM RUM\\M RaPlP RVX[X Rf[j[ RSPS[ RUIUM RVSVY RXCXG RVS[S[X RUI\\I\\M RcUcY RaFjFjL RhTiV RdCeF ReUfX R`V_Z RSP^P][^[[[ RaFaSkSj[',
	    'PoWOgO RWRgR RWTlT Re[i[ RSJSN RWMgMgR RSJkJkN RdXeY R_X`Z RUXS[ RjDhF RVDTF R]D\\F RbD`F R_J]M R[X[[ RYF^H ReFjI ReCdG`I RZCXHTI RWMWVjVi[',
	    'PoTI]I RaIjI RaMjM RSO]O RaOlO RSR]R RSU]U RSKSU RTETH RTE]E]H RSK]K]U RcUdY RaFjFjL R[W]Z RhUiW RVWR[ RdCeF RfUgX RaV`Z RaFaRlSj[f[',
	    'PoUHhH RVJhJ RRLkL RXUkU RSESH RWPgOfS RSEkEkH R^Y_[ RWYT[ RZYZ[ RdGdMlQ RYOXWiWh[ RZGZMRQ Re[h[e[ R[SXS R[S^SaS RXQfRcS`S]S',
	    'PoaIjI RaLjL RTM]M RaOlO RZ[][ Rf[j[ RWPW[ RYP^P^[ RcVcY RaFjFjL R[J^M RhUiW RWE[E_I R]PYT R]TYX RUFRI RVPST ReCeF RWTRX RfUgX RWHUM R`U_Z RRPWP RS[V[S[ RaFaRlSj[',
	    'PodElE RSIZI RSLZL R]MbM RSO[O R]XbX RWUWY RSFZFZL RSFSR[R[Z R]G][ R]GbGbM R]RbRbZ RdJdW RhEh[ RVCVF RYUYW RZ[X[ RUUUY RSURZ RdJkJkViV R`C_G',
	    'PoaIjI RUL^L RaMjM RaPlP RUP^P RUT^T RUY^Y Rf[j[ RSFSJ RULU[ RZKZX RSF^F^J RcVcY RaFjFjL RhTiW RdCeF RfUfX R_U_Z RaFaRkSj[ RYDULRN',
	    'PoaIjI RTK[K RaMjM RaPlP RTW[W RTRTW RTGTL R[H[L R[T[W RUQ^Q^Y RdUdY RaFjFjL RhTjV RWWTZ RUERH ReCeF RfUgX RWKUOTS R]ZZZ Rj[g[ RaV`Z R]NZN RWHWM RaFaSlSj[ RWSWX RUF^F]N',
	    'PoTF_F RaIjI RaMjM RTM`M RaPlP RVQ^Q RVZ^Z RVTV[ RZGZT RVJ^J^Q RVT^T^[ RcVdY RaFjFjL RiUjW RYC[F ReCeF RfUgY RaU`Z Rj[g[ RaFaSlSk[ RTFTSR[',
	    'Po`IjI RSJ\\J R`MjM RSM\\M R`PlP RRQ]Q RSZ\\Z Rf[j[ RRFRI RSTS[ RXHXT RST\\T\\[ RRF]F]I R_U_Z RcVcY R`FjFjL RWCXF RdCdF ReUfX RhUhW R`F`RlSj[',
	    'PoaIjI RSJWJ RZJ_J RaMjM RaPlP RS[W[ R[[^[ RREWEWJ RSJSOXOXZ RYE_E_J RZJYO_O_Y RdVdY RaFjFkL RiUjW R]PZT R]TYX RUPRS RVURX RfCfF RfUgX Rj[g[ RaU`Z RaFaSlSk[',
	    'PoaIjI RaMjM R\\M^M RaPlP RSU]U RSZ]Z Rf[j[ RSESM RSQS[ RXQXZ RZEZO RSQ]Q][ R^E^M RdUdY RaFjFjL RhTjV ReCeF RfUgX RXDSF RZE^E R`U`Z RWKRM RaFaSlSj[ RWGVN',
	    'PoSFVF R[F^F R`IjI RRJVJ RZJ^J R`MjM R`PlP RUR\\R RUV\\V RY[\\[ Rf[j[ RUNU[ RTO\\O\\[ RcVcY R`FjFjL RXI^O RhUiW RWKSP RdCeF ReUfX R`U_Z R`F`SlSj[ RXDWL',
	    'PoaIjI RaMjM RaPlP RTS\\S RTV\\V Re[j[ RX[\\[ RTDSLSOT[ RTD\\D\\L RTO\\O\\[ RRL^L^O RcVcY RaFjFjL RhUiW RdCdF ReUfX R`U_Z RXHWL RaFaRkSj[ R\\GXH',
	    'PoSH`H RbIjI RbMjM RRN`N RbPlP RSR^R Rg[k[ RXDX[ R[E[[ RSK_K_R RdUdY RbFjFjL RZR_W RfCfF RUVRY RgUgX RiUjW RaU`Z RWRTW R_DYDSE RbFbSlSk[',
	    'PoTH^H RaIjI RTJ^J RaMjM RTM^M RaPlP RTQ^Q RRU_U RUFUQ RYFY[ RcUcY RaFjFjL RhUiV ReCeF RfUfX RVCRJ RYDYI Rj[g[ R`U_Z RaFaSlSj[',
	    'Po`GlG RRG\\G R`LjL RSN[N R`OjO R`RlR RSR[R RRV\\V RSJSR RWCWJ RWRW[ RSJ\\J\\R RbWbZ ReWeZ R`IjIjO RiViY R_W^[ Rg[j[g[ RaC_I`TkTj[',
	    'PoRFlF R_MjM RWQgQ RWSgS RWUlU RSMZNgOgS R_Y`Z RYC[F RhKkN RUYS[ RdK`M RgIcJ R[HXJ RdCbF ReFbH RZYZ[ R`IcJ RWOWWjWi[ RYFUIWKSM Re[i[e[',
	    'PoRI_I RaIjI RaMjM RaPlP Rg[j[ RaU`Z RdUdY RaFjFjL RhTjV ReCeF RUDVH RfUgX RUJSPVRRZ R\\JZP]RYZ RWOTX R\\C[H R]O[X R]V^ZXZ RWVXZRZ RaFaSkSj[',
	    'PoaIjI RVI\\I RaMjM RaPlP Rf[j[ RUSUZ RWSWY RZSZY RcVcY RaFjFjL RgUiW RUFSJ RdCeF RWM\\M^R RWE\\D^J ReUfX RUNSS R`U_Z RaFaSlSj[ RUS]S^XRZ',
	    'PoaIjI RaMjM RUO^O RaPlP RRS_S RTY]Y Rf[j[ RTUT[ RYNYY R^U^Z RcVcY RaFjFjL RUHXK RhUjW ReCeF RfUfX RVMTQ RYCUGXG^FZJRN RWFRH RaU_Z RaFaSkSj[',
	    'PoTH]H R`IjI RTL]L R`MjM R`OlO RRS^S Rf[j[ RTETL RXLYO RTE]E]L RSOSP^O^Q RbUbY R`FjFjL RgUiW RdCeF ReUfX RUV]Z RWPTW R_U_Z R[SZWXYS[ R`F`RlSj[',
	    'PoaIjI RaMjM RaPlP RRQ^Q RRU^U Rf[j[ RXFXI RcVcY RaFjFjL R\\N_Q RTLWO RXV\\Z RhUiW RXVTZ RWJUM R[KUQ ReCdF R\\F^J RfUfX RUESJ R`U_Z RYRWY RaFaSlSj[ R]DTE',
	    'PoRE_E RaIjI RaMjM RSO^O RaPlP RRR^R RSISN RWEWN RYPY[ RZEZN RSI^I^N RdUdY RaFjFjL RXR]X RhTiW ReCeF RYRRY RfUgX Rg[j[g[ R`U`Z RaFaRkSj[',
	    'PoTH]H R`IjI R`MjM RUM]M R`PlP RUP]P RUS]S RRU^U Rf[j[ RRLRU RTCTH RUKUS RUK]K]S RcVcY R`FjFjL RZW]Z RgUiW RVWR[ RdCdF ReUfX R_U_Z R\\DTE R`F`SkSj[',
	    'PoRE_E RTH^H RUL]L RiMlM RTS]S RTU`U R[[^[ RVXVZ RYCYH RTN]N]S RSJSK_J_L RcEcK RcEiEjL RlJlL RcPeVhYl[ RSXR[ RTNTW^W^[ RaPjPhTdY_[ RYQ]Q RXLWN RWQTQ',
	    'PofHlH RSIZI RSLZL RSO\\O Ri[l[ RSFZFZL RfFfK ReTiTiZ RlXl[ RYTYW RWUWX RaKkKkN RVCVF RSURZ ReTcZ R[[X[ RfLfRkQlR RSFSR\\R[W[[ R^E^S]Z RaKaS`Z RjD`E RiNcO',
	    'Po[H`H RbIjI RbMjM RbPlP R[Q_Q RbSlS Rg[j[ R[F[Q R]S]Y RWK`K`M RbUaZ RbFbS RdUeY RbFjFjL RfCfF RgUgX RjUjW R`X\\Y RYTXZ RlSk[ RTETSRZ RWKVSUZ R_CTE R^NXN',
	    'PoRE^E RaIjI RSL\\L RaMjM RaPlP RUV[V Rf[j[ RSOS[ RSHSL RXVXZ RSH]H]L RcUcY RaFjFjL RhTiV RdCeF ReUfX RVQZS R`U_Z RZPXSTT RSO]O][[[ RaFaSkSj[',
	    'PoSF_F RaIjI RaMjM RUO]O RaOlO RSS^S RV[Y[ Rg[j[ RVCVH RYOY[ R\\C\\H RdUdY RaFjFjL RhUiW RVLRN RYITM R\\U]X RfUgX RYJ]L RUUTZ ReCeF R`U`Z RaFaRlSj[',
	    'Po`EkE R`IkI RSI\\I RSM\\M R`MkM RSO^O RY[\\[ RRURZ RVUVY RSF\\F\\L RcTcY RfEfQ RZT\\V RhTjV RWCWF RXUYX RfTfX R_U_Z Rk[g[ R`E`QlRk[ RSFSR^S\\[',
	    'PoTI]I R`IjI RTL]L R`MjM R`PlP RTP]P RRS^S RTFTP RXPX[ RTF]F]P RbVcY R`FjFjL RWS\\W RhUiW RXCXF RdCdF RYSRY ReUfY R`U_Z R`F`SkSj[f[',
	    'PoaIjI RSI]I RaMjM RSM]M RaOlO RSP_P RTV]V RSFSM RXSX[ RSF]F]M RcUcY RaFjFjL RYP]T RhUjV RXCXF RdCeF RWPRU RfUfX R`U_Z RWNVR RaFaRlSj[g[',
	    'PoRF^F R`IjI RTL]L R`MjM RTO]O R`PlP RTR]R RRU^U Rf[j[ RTITR RXCXI RTI]I]R RcVcY R`FjFjL R[W\\Z RgUiW RVWR[ RdCeF ReUfX R`U_Z R`F`SlSj[',
	    'PoUH]H RaIjI RUK]K RaMjM RTN^N RaOlO RWQ^Q RTT^T RUEUK RWSWZ RUE]E]K RdUdY RaFjFjL RiTjW R^U\\W ReCeF R[U]Z RgUgX RaU`Z RZYVZ RTNTTS[ RaFaRlSj[g[',
	    'PoZG^G RaIjI RaMjM RSO^O RaPlP RdUdY RaFjFjL RhTjV RdCeF RSGVGVJ RXLXO R[G\\J RfUfX RTDRJ Rj[g[ RUQWX RZDYJ R[QYX R`U`Z R]XRY RaFaRkSj[',
	    'PoRE_E RbIjI RSK^K RbMjM RbPlP RRQ_Q RRT_T Rg[j[ RYCYQ RSH^H^N RcVdZ RbFjFjM RSHSN^O`R RUVXX RiUjW ReCeF RgUgY RaV`Z R\\R\\[X[ RbFbSlSk[',
	    'PoTG_G RaIjI RUL_L RaMjM RaPlP RWR\\R Rf[j[ RWHWR RYUYY RYCYG R[U\\X R\\H\\R RcVcY RaFjFjL RhUiW ReCeF R^T_W RfUfX RVUUY R`U_Z RaFaSlSj[ RTGTTRZ',
	    'PoTE]E RbGlG RRG_G RSI^I RWT_UlU Re[i[ RYCYI RVL\\K\\N RdYfY R_Y`Z RUYS[ RWJSN RZYZ[ RbGfKlN RWPWWjWi[ RcC`I RiGfK`M RYN\\NYM R[RWQYQhPhRdQ R\\R_QcQ`Q]Q',
	    'Po_HjH R_JjJ RTL[L R_LkL RWQgQ RWSgS RWUlU RTETL RTE[E[L R`D`L ReDeL RWNhNhS R_Y`Z RUYS[ R`C\\H RZY[[ RWNWWjWi[e[',
	    'PoSD^D RVG]G RhGlG RUH^H RaIjI RWS_TlU RbDhDhG R_Y`Z RUXS[ RbDaG RZYZ[ RYI^K RSDSM^M\\O RYJUK RdLlN RdRhR RYFYJ RgL`M R[RWQWWjWi[f[ RWQYQhPgRdQ RbR_QbQ R\\R_Q\\Q',
	    'PoRE^E R`IjI R`MjM RVM\\M R`PlP RZU^U RRX^X Re[j[ RRERZ RVHVM RUPXPXU RZPZU RVH\\H\\M RZP^P^U RbVbY R`FjFjL RgUiV RdCdF ReUfX R`V^Z RWPTPUUXUTU R`F`SkSj[',
	    'PoRE_E RbFlF RWQhQ RWShS RWUkU RXCXM RSG^G^J RWOhOhS R_Y`Z RUYS[ RdD`H RZYZ[ RbGfKlM RiGeK`M RYJRM RSGSJZJ]K RWOWWiWh[e[',
	    'PoTD]D RRF_F RRI^I RSK^K RWQgQ RWSgS RWUlU Rf[i[ RYCYG RYIYM RWOgOgS R_Y`Z RdI`M RVXS[ R[F[I R[Y[[ RVGUI RaHgK RWOWWjWi[ RlKkMlM ReCcK RaFiFjM',
	    'PobIjI RbMjM RbOlO Rg[j[ RSDXDXL RZD_D_L RdUdY RbFjFkL R[O_R RiUiW RVFSI RVISM R]F[H R^I[L RfCfF RfUgX RYN\\O R`U_Z RXNVPRR RYRUT R[TUW R]WU[ RbFbRlSk[',
	    'PoTFaF RcIkI RVKaK RcMkM RWN_N RcOlO RWT`T RWW`W R][`[ Rh[k[ RWQW[ R[E[[ RWH_H_N RWQ`Q`[ RbUbZ ReUeY RcFkFkL RiUjW RgUhX RfCfF RcFcRlSk[ RTFTSRZ',
	    'PoRH`H RbIjI RTK_K RbMjM RbPlP RSQ_Q RSMSQ RTETK RTE_E_K RSM_M_Q RaV`Z RdUdY RbFjFjL RhTjV ReCeF RfUgX RVW]Z RYCYPVW RRT_T[WTZ Rg[j[g[ RYMXT RbFbRlSk[',
	    'PoSH^H RaIjI RaLjL RaOlO RRP^P RST^T RRW_W RULUS RYEY[ RSESL]L\\S RSE^E^L RaU`Z RdUdY RaFjFjL RhTjV ReCeF RfUgX Rg[j[g[ RaFaRlRj[',
	    'PoRG^G R`IjI R`MjM R`PlP Rf[j[ R`U_Z RcUcY R`FjFjL RZJ]L RZP]S RSJVL RSPVS RgTiV RVWR[ RUISM RdCdF RXCXR[Y ReUfX RUOST R\\IZM RXNZW RXQTY R\\OZT R`F`SkSj[ RXMWV',
	    'Po`ElE RRE]E RaHjH R^JlJ RWQhQ RWShS RWUlU RWOhOhS R_Y`Z RUYS[ RaC^F R[Y[[ ReGeJgLlM RdK`M RTLRM RWDVFUL RVI[I[MXL RWOWWjWi[e[',
	    'Po`IjI R`MjM RTO\\O R`PlP RTT\\T Rf[j[ RTITS RYJYS RZVZZ RSK\\J\\S RcVcY R`FjFjL RhUiW RdCdF R\\V]Y RfUfX RVCSL RVF[FXL RTVR[ R`V_Z RWVWZ R`F`SkSj[',
	    'PoaIjI RaMjM RRM_M RaPlP RRS`S Rg[j[ RUEUS RWVWZ RXFXS R[F[S RTG_G^S RaUaZ RdUdY RaFjFjM RhUiW ReCeF RZVZZ R]U^Y RfUgX RUCSJ RTVS[ RaFaSlSk[',
	    'PoUElE RWHaH RXK`K RYS`TlU Rg[j[ R\\X\\[ RYOYWjWj[ RXXV[ RdHkHiJ RdFbJ RaXaZ RfKkN R[LWN ReLbM R\\H[M RYQ\\QYQ R\\Q_QbQ_Q RYOhOhRdQbQ RUEURS[',
	    'PoRE_E RaIjI RSL^L RaMjM RaPlP RTQ]Q RTT]T RRW_W RSHSK RTNTT RWEWK RXTX[ RZEZK RTN]N]T RSH^H^K RdUdY RaFjFjL RhTjV ReCeF RfUgX Rg[j[g[ RaV`Z RaFaSlSj[',
	    'PoSIWI RYI]I R`IiI R`MiM RSO]O R`OlO RSS]S RRV^V Rf[j[ RSDSI RSKSR RSDWDWI RXKX[ RYDYI RSK]K]R RYD]D]I RcUcY R`FiFiL RgUiV RdCdF ReUfX R_U_Z R`F`RkSj[',
	    'PoiGlJ R_IkI RRI]I RWQgQ RWSgS RWUlU RXIXM RfIfM RWOhOhS R[J\\L R_Y`Z RiJkM RbJ`M RUYS[ RWCUF RUJRM RdCbF RZYZ[ RSEVF R`FcG RfFaI R[EXGTI RWOWWjWi[f[',
	    'PoUI^I RaIjI RUL^L RaMjM RUO^O RaPlP RUS_S Rf[j[ RVGVS RWVWZ R[F[S RcVcY RaFjFjL R]U]X RhUiW RdCeF RZVZY ReUfX RWCSL RTVSZ R`U_Z RaFaSlSj[',
	    'PoRF^F RSI^I RaIjI RaMjM RTN\\N RaPlP RTU]U RTQTU RXCXI RTQ]Q]U RRLRM^L^N RdUdY RaFjFjL RhTjV ReCeF RVVVY RfUgX Rj[g[ R`U`Z RaFaSlSj[ R[VZYRZ R^YVZ',
	    'PoRF_F RaIjI RaMjM RRO_O RaOlO RSS^S RRW_W RUKUS RVCVI RXKX[ R[C[I RSK]K\\S RdUdY RaFjFjL RhTjV RdCdF RgUgX RaUaZ Rj[g[ RaFaRkSj[',
	    'PobGlG R\\M^M RWSgS RWUlU Rf[i[ RTGTM RYCYM RTG^G^M R_Y`Z R\\D_G RVYS[ RUDSG R[Y[[ R[I\\K RbGfKlM RWOWWjWi[ RVIUK RcCaH RiGfKaM RWQ[QWQ R\\QZQ R\\Q_QbQ R`Q]Q RWOgOgRcQ`Q',
	    'PoTH]H RWQgQ RWTlT Rf[i[ RRLRO RaDjDjJ RRLlLlO RdXeY R_X`Z RUXR[ RZXZ[ RWNWWjWi[ RdJgJ RWFTETJWJZJ RjFgF RTFUE]D]JYJWJ RdFaEaJcIjIgJdJ RWNgNgRcR RdFgF RZF]FYF RZSVR[R^RbR_R\\R',
	    'PoRH`H RbIjI RTL^L RbMjM RbPlP RTQWQ R\\R_R RTT_T R\\W_W Rg[j[ RWNVR RTNTXWWW[ RYCY[ R\\W\\[ RTE^E_K R_N_[ RaUaZ RdUdY RbFjFjL RfCfF RgUgX RjUjW R\\N\\R RbFbSlSk[ RSSR[',
	    'PoSF^F RaIiI RSJ_J RaMiM RaOlO RSR^R RSV^V RSNSV RVCVJ RYJYV R[C[J RSN^N^V RdUdY RfUfX RaFiFiL R[W^Z RdCdF RhUiW RVXRZ Rj[f[ R`U`Y RaFaSkSj[',
	    'PoaIjI RRJ^J RaMjM RaPlP RTU]U RTZ]Z Rf[j[ RTQT[ RYEYP RXQXZ RTQ]Q]Z RcVcY RaFjFjL R[M]O RhUiW RYJRQ RdCeF RUGUI ReUfX RXK[M R\\F\\I R`U_Z RaFaSlSj[ R]DSE',
	    'PoRH`H RbIjI RbMjM RUN\\N RbPlP RWX[X R\\[^[ Rg[j[ RSQS[ RVJVN RWTWY RWT[T[X RTK]J]N RSQ_Q_[ RdUdY RbFjFjL R[H_L RhTiV RWHRL ReCeF RfUgX RaUaZ RXEUK RbFbSkSj[ R]DSE',
	    'PoSG_G RaIjI RTM^M RaMjM RUO]O RaPlP RUR]R RV[X[ Rf[j[ RUJUR RYRY[ R]J]R RcVcY RaFjFjL RhUiV RSHVJRM R^H]K ReCeF R\\U]X RZG]L ReUgX RXDTL RUUSY R`U_Z RaFaRlSj[',
	    'PoRElE R[GcG R[LcL RfMlM RVShS RVUkU R[C[G R[J[L R[JcJcL RcCcG RgGgM R_Y`Z RUYS[ RZYZ[ RkIfJ RdQhQ RSIXHXLRM RVOVWjWi[f[ RVQZQ\\Q_Q\\Q R`QcQ RVOhOhRdQbQ_Q',
	    'PoTI]I RaIjI RTL]L RaMjM RTO]O RaPlP RSQVQ RZQ]Q RSTVT RZT]T RRW^W Rf[j[ RTFTO RXPX[ RXCXF RTF]F]O RcVcY RaFjFjL RhUiV ReCdF RfUfX R`U_Z RaFaRkSj[',
	    'PoRD^D R`FlF RWQgQ RWUlU Re[i[ RXIXM RZYZ[ RTG]G]J RfHfMlLlM R_Y_Z RUYS[ RUKRM RTGTI\\J^L RcYeY RhDjD RWOWWiWh[ RcK_M ReCdK R[SWS[S^SbS R_S\\S RWOgOgScS_S',
	    'PoRHWH RYH^H RaIjI RRLWL RYL^L RaMjM RaPlP RVRZR RVUZU RXYZY Rg[j[ RRER[ RREWEWL RYEYL RVOZO[Y RdUdY RaFjFjL RiUiW ReCeF RgUgX RaU`Z RYE^E^[\\[ RaFaSlSj[ RVOVUUY',
	    'PoRHWH RZH^H RaIjI RRLWL RZL^L RaMjM RaPlP RTQ\\Q R[[^[ Rg[j[ RRER[ RREWEWL RXNXY RZEZL RZE^E^[ RdUdY RgUgX RaFjFjL RiUiW RfCfF RXO[V RXOUW RaU`Z RaFaSlSj[',
	    'PoTI^I RbIjI RVL]L RbMjM RbPlP RUP`P RWV^V RWZ^Z Rg[k[ RWSW[ RZJZP RWS^S^[ RTD^D_I RcVdY RbFjFjL RiUjW ReCeF RfUgY RaU`Z R_LXSSU RbFbSlSk[ RTDTSR[',
	    'PoSI^I RaIjI RaMjM RTO]O RaOlO RTR]R RTV]V RSDSI RTLTV RVDVI RZDZI RTL]L]V RSD^D^I RdUdY RaFjFjL R[W]Y RhUiW RUWSZ RfUfX RdCdF R`U`Z RaFaRkSj[f[',
	    'PoRG_G RaIjI RaMjM R[M_M RSO_O RaPlP RRU_U RX[\\[ Rf[j[ R[F[L RSJSR]R\\[ RSJ_J_R RcVdY RaFjFjL RUVWY RUCWE RhUjW R\\C[H RWGWLUN ReCeF RfUgX R`V_Z RaFaSlSk[',
	    'PoSE]E RaIjI RaMjM RaPlP RVY[Y Rf[j[ RSPS[ RVUVZ RZGZN RVU[U[X RSP^P^[ RdVdY RaFjFjL RiUiW RRI^I]L RdCeF RfUgX RVG[I RWPUT R`U_Z R]EXG RXIVLRM RZPZS^S RZ[^[Z[ RaFaRlRj[',
	    'PoTElE R^JjJ R^NkN RYTiT RYVlV RZHZN RXYV[ R[FVK R_F[K R[Y[[ R`Y`[ RYPYWjXj[g[ RgLjL RaR_R RaRcR RYR]R`R RTETSS[ R`C_G_McL_L RdFdMhLcL RYPiPiSeRbR',
	    'PoSG\\G RXI]I RaMiM RWQgQ RWSgS RWUlU Rd[i[ RSDSG RTITM RZYY[ RSD\\D\\G RaIaM RWNgNgS RaJjJjM R_Y_Z RUYS[ RcYeY RWNWWjWi[ RcD^F RbEfGlI RbEjEeG_I RXGYLRM R]LVM',
	    'Po\\FdF RSIZI RSLZL R]NcN RSOZO RSFZFZL R]I]M R]IcIcM R_C`F RfYd[ RUCUF RUUVWWX RgRk[ RiQeZ RfNiX RSURZ RY[V[ R\\QcQ`S`[][ RfJhW RgCdO RdU\\V RSFSRZSY[ RfIkIhU',
	    'PoRHWH RZH_H RbIjI RRLWL RZL_L RbMjM RbPlP RVS[S RVW[W RRDR[ RVOVW RRDWDWL RZDZL RVO[O[W RaUaZ ReUeY RbFjFjL RiTiW ReCeF RgUgX Rk[g[ RZD_D_[\\[ RbFbRlSk[',
	    'PoUFZF RdFhF RdIhI RVI[I RWPgP RWRgR RWTlT Re[i[ RSKSN RVDVK RWNgNgR RcDiDiK RSKkKkN R_Y`Z RUXS[ R_K\\N RZYZ[ R]HbI R`H\\J RZCUD R]DaD\\F RWNWWjWi[',
	    'PoSF^F RaIjI RRJ_J RaMjM RaPlP RTP^P RTS^S RV[\\[ Rg[j[ RTMTS RWVW[ RYCYF RTM^M]S RdVdY RaFjFkL R]V^Y RWU[V RiUjW ReCfF RVGVJ RfUgX RTVRZ R\\F[I RaU`Z RaFaSlSk[',
	    'Po`EkE RTG]G R_HlH R`KkK RUM]M RWQgQ RWSgS RWUlU RTD]D]G RUI]I]M ReHeM RWOgOhS R_Y`Z RVXS[ RZYZ[ RbEcH RhEhH RWOWWjWi[f[ RTESN',
	    'PoaIjI RSI^I RaLjL RaOlO RSU[U R[[^[ Rg[j[ RSDSI RSPST RWNWX R[D[I RSP[P[T RSD^D_I RTM_M_W`Z RdUdY RaFjFjL RZV\\Y RhUiW RfUfX RWDVISO RdCdF RaFaRkRj[ R^T^[ R[XRY',
	    'PoRElE RUHjH RUMhM RTSXS RTVXV Ri[l[ RV[X[ RUEUH RUJUM R\\X\\Z RUJhJhM RdOiOjZ RlXl[ RfRgW RTOXOYYZ[ R[P[VbVa[^[ R^W^Y R^S[S_S R[PaPaS^S RTOTVS[ RdOdVc[',
	    'PocIjI RcMjM RcPlP RXRaR RWUaU R\\F\\H ReUeX RcFjFjL RSDVF R^OaR RYL\\O RRKVM R\\V`Z RiUjV R_E`I R[WX[ R\\JYL R_LYQ RgUgX RbUbY RYEXJ RVSRZ RfCfF Rj[g[ R\\RZY R`DYE RcFcSlSj[',
	    'Po_FkF RTI[I R_IjI RTM[M R^MkM RTP\\P R^SlS RW[[[ R_[b[ RXUXX RZTZW RTF[F[L RbOb[ ReFeM RiOkP RaCbF RXCXF RhCgF RSURZ RkUgYcZ RdO^O ReV^W RlYl[i[ RfOhY RTFTR\\S[[',
	    'PocGlG RTH]H RRJ_J RVL[M]N RVT_UlV RTETH RUYS[ RZYZ[ R_Y_[ RbGgKlM RcCaH RWLRN RjGfK`M RZF^F RYRVQVWkWi[g[ RXF[F RVPhPhSdRhR RWRZR RWFTE]E]GZFWF R_RbR R_R\\R_R ReRbR_R',
	    'PoaIjI RSI_I RSL^L RaMjM RRO_O RaOlO RRT_T RSW^W RSDSI RWDWI RYJYO RYTY[ R[D[I RSD_D_I RcUcY RaFjFjL RhUiV R\\Q[T RdCdF RVQVT ReUfX R`U_Z RaFaRlSj[f[',
	    'PoYE_E RbIjI RUJ]J RYL_L RbMjM RbPlP RWQ_Q RVS_S RUW_W RWNWQ RYCYL RWN_N_Q RTH_H_J RdVdY RbFjFjL RZWWZ RfUgX RiUiW RZX_Z ReCeF RZTYY Rj[g[ RaUaY RbFbSlSk[ RTHTSR[',
	    'PoRF_F RaIjI RaMjM RVM[M RTO^O RaPlP RTT]T RTW]W RXZ^Z RSZZZ RTHTO RTQTW RYCYF RVJVL[J[M RTQ]Q]W RTH^H^O RdUdY RaFjFjL RhTjV ReCeF RgUgX Rk[g[ RaU`Z RaFaSlSk[',
	    'PoRH`H RaIjI RaMjM RSM`M RaOlO RTQ_Q RST`T Rg[j[ RXCXH RYMY[ R[C[H RdUdY RaFjFjL RTDVG R\\V_Y RXT]W RhUjW R^D]G RWJWM RfUgX R\\I[M ReCeF RaU`Z RYTUXRY RaFaRlSj[',
	    'PoRG[G R]HjH R[JlJ RgOjO RVT_UlV RWDWN R]LkKjN RWGZJ RUYS[ RfKcN RXGRM RZYZ[ R_Y_[ RaJ[L R]D]H ReRhR RfFjF RaF]F RVRYRVQVWkWj[f[ RbReR R^R\\R RVPhPhSeRbR RaFdFfF RbL^N_P R]DjDjGfFcF RYR\\R_RbR_R',
	    'Po[GaG RcIjI RTLYL RcMjM RZNaN RcPlP R[TaT Rg[j[ RTETSU[ RTEYEYK R^N^[ R`H_M ReUeY RcFjFjL R\\I\\M RgUhX RjUjW RfCfF R^C^G RbUbZ RcFcSlSk[ RTORY RUQYQYXUY',
	    'PobIjI RWL\\L RbMjM RbOlO RRR`R RSU_U RV[Y[ RTNTR RWIWL RXNXR RZUZ[ R[N[R RTN^N^R RSF`F`I RdUdY RbFjFjL RYH[I R]J`L RiTjW RXDZF RSFTITL RgUgX ReCeF RaU`Z Rk[g[ RbFbRlSk[',
	    'Po^ElE RSI\\I R`KkK RSM\\M R`MjM R`OjO RSP]P R`RkR RRURZ RVUVY RSF\\F\\L RaIaS RcCcG RfJfR RhChG RZT\\V RWCVF RXUYX RbWj[ R\\[Y[ R_UkUhXdY^[ RSFSR]S\\[',
	    'PoSD`D RbIjI RbLjL RbOlO Rg[j[ RSIS[ RYDY[ ReUeY RbFjFjL R[N^P RUNXP R[T^W RUUXW R\\F`J RgUgX RiTjW RWLUR RWSUY R]L[R R]S[Y ReCeF RaUaZ RVFRJ_J_[\\[ RbFbRlSj[',
	    'PoTF]F RaFjF RTH]H RaHjH RaKjK RVQgQ RVSgS RVUlU Rf[i[ RTD]D]K RaDaK RVOgOgS RaDjDjK R_Y`Z RhLkN RUXR[ RcL`M RULSN RZYZ[ RTDTK[K\\M RVOVWjWi[',
	    'PoRF`F RbIjI RbLjL RUM]M RbOlO RSP_P RWCWH R[C[H RSJSK_J_L RdUdY RbFjFjL R[U^X RfUgX RiUiW ReCeF RaU`Z R[PSS RYTSV RXR[UXWRZ Rj[g[ R^RZTZY RZ[W[ RbFbRlRk[',
	    'PobIkI RbMkM RbPlP RUQ_Q RUS^S RUW^W RUZ_Z Rg[j[ RUOU[ RSDXDXL RZOZZ RZD_D_L RaU`Z RdUdY RbFkFkL RiTjV RVFSI RVJSM R\\FZI R]JZL RfCfF RgUgX RVMSS RbFbSlSk[',
	    'Po[DbD RZFcF RdGlG R[NbN RWThT RWVlV Re[i[ R[L[N R_N]P R[LbLbN RcYeZ R_Y`[ RiDkF RUYS[ RfKcN RZYZ[ RgGjN RWPWWjWi[ RUDWERG RWIRK RXFXNUN RZRVR R[HaIbJ RWPhPhSdRhR R^J[J R]J`J]J RgCfL R\\R_RbReRbR_R\\R',
	    'Po]EbE RUGZG RdGhG RVIZI RdIhI RRLlL RXQfQ RXSfS RXUkU Re[h[ RVEVK R]I]K RWOgNgS RdDiDiK RcXdZ R^Y_[ RVYT[ RZYZ[ ReLgNlP RXNXWiWh[ RYLWORQ RZDUE R]C]GaGaL',
	    'PoSH^H RaIjI RSM^M RaMjM RaPlP RRUWU RZU_U RZZ_Z RRZWZ Rg[j[ RRPR[ RSDSL RUPUZ RRPWPW[ RYDYL RZPZ[ RZP^P\\Z RSD^D^L RdVdY RaFjFjM RiUiW RfUgX R_P`XaZ ReCeF RaFaSlSj[',
	    'Po_HjH RTI\\I RTL\\L R_NkN RTO\\O R^QlQ RX[[[ RVUVX RTF\\F\\L R_EjEjK RaUcW R_E_KiKgRkV RbKbR^V RhUfW RZTZV RXUYX ReVkY RWCVF RSURY ReV^Y ReSd[b[ RTFTR]R[Z',
	    'PoZE_E RbIjI RVK]K RbMjM RYM_M RbPlP RWQ_Q Rg[j[ RVUVZ RYUYZ RZIZM RZCZH RWO_O_S RTH`H`K RbUaZ RdUeY RgUgX RbFjFjL RfCeF RjUjW R[O[T^S RWOWSZSWS RbFbSlSk[ RTHTTR[ R\\U\\ZVZ RVU_U_YXZ',
	    'PoTD^D RbIkI RbMkM RTMWM R[M^M RbPlP RTQ_Q RTT^T RTV^V RTZ_Z Rg[j[ RUOU[ RYDYM RZOZZ RSGSI_G_J RaUaZ RdUdY RbFkFkL RiTjW RfCfF RgUgX RUNRT RUJWJUJ R[J]J[J RbFbSlSk[',
	    'PoaDlD RSE^E RRG_G RaMfNlN RVT_UlV RTI]I]O RbHbN RVEVG RUYS[ R[E[H R_Y_[ RZYZ[ RTITN[L]K RWKTK ReKaK RVPVWkXj[g[ RbReR RYKWK RaCaFjGgH RYR\\RYRVRXQhPhSeRbR RdHaIhJjK RdKgKdK R\\R_RbR_R\\R',
	    'PoTD`D RcIjI RUJXJ R\\J_J RcMjM RTMXM R\\M`M RcPlP RRRYR R[SaS R[VaV R[ZaZ RSGSJ RVOV[ RZDZM R[P[[ RSG`GaJ R[PaPa[ ReVeY RcFjFkL RVSYV RgUhX RiUjW RcUbZ RVQSX RfCfF Rk[g[ RcFcSlSk[',
	    'PobIjI RUI]I RbMjM RbPlP RXP[P RTV_V R][_[ Rf[j[ RSLSP RTRT[ RWRW[ RXL[L[P R[R[[ RTR_R_[ R]L`L`P RdVdY RbFjFjL RhUjV RZC_I RYCRJ RfUgX RaVaZ ReCeF RXLXP RVLSL RVLVPSP R_L\\L]P`P\\P RbFbSlSj[',
	    'PoSGXG RZG_G RaIjI RZJ_J RSLXL RZL_L RaMjM RaPlP RRS`S Rg[j[ RSDSL RSDXDXL RZDZL RZD_D_L RdVdY RaFjFjL R^N_P RiUiW RTNRP R[NYP ReCfF RgUgX RUV]Z RWQUW R`U_Z R\\S[WYYR[ RaFaRlRk[ RWJSJ',
	    'PoRF_F RbIjI RbMjM RSMXM RZM_M RbPlP RTQ_Q RUT^T RUV^V RUY_Y RSISM RUPU[ RVCVG RSIXIXM RZOZY RZIZM R\\C\\G RZI_I_M RaUaZ RdVdY RbFjFkL RiUjW RfCfF RfUgX RUNST Rk[g[ RbFbSlSk[',
	    'PoSGXG R[G_G RbIkI RSJXJ R[J_J RbMkM RSMXM R[M_M RbPlP RTQ_Q RUS^S RUW^W RUZ_Z Rg[j[ RSDSL RUOU[ RSDXDXL RZOZZ R[D_D_L RaUaZ RdUdY RbFkFkL RiTjW RfCfF RgUgX RUMRT R[D[MYS RbFbSlSk[',
	    'PoSEXE R[E`E RbIjI RbMjM RSM`M RbPlP RSS`S RVVZV R[[_[ Rg[j[ RSGSJ RVHUJ RVSV[ RXMXS RSGXGXJ R\\M\\[ R]H]J R[G`G`J RSP`P`S RdUeY RbFjFjL RfCeF RgUgX RjUjW R_V[W RbUaZ RYZTZ RbFbSlSk[ RSMSURZ R[GZM',
	    'Po\\DbD R[FcF RRJYJ RdJlJ R\\NbN RWT_UlV Rd[i[ RVJVO RZYZ[ R\\L\\N R\\LbLbN RhJhO RSEUH RdYfY R_Y`[ RdFgH RUYS[ RjEeJ ReLdO RVDSF RXESJ RhDeF RYLYN RkLkN RWPWWjWh[ RSLSO RdRgR R[RWR R\\H`IbJ R_J\\J R]J`J^J RWPgPgSdR R\\R_RbR RcR`R]R',
	    'PoRUfU Re[j[ R^C]F R[IaK RWFhFgMcM RWFWPlPj[',
	    'Po`FkF RfNjN R_ViV Rf[j[ RdIfK RRJ[J\\UZV RcCcF R`F`RlRk[ RVDVPTY RjGjN',
	    'PofNiN R^UiU Rf[k[ RdCcF R_F_QlQlY RcIfK RWSRY RVLXO[V R[KVV RSG\\GYR R_FjFiN',
	    'PocFkF R^FgF R\\GdG RcQfQ RRWeW Rd[i[ RWKkJlK R^I[K R[N`P RdDfD RWKWSjSi[ RSGZG`FcIhKfQ',
	    'PoeNiN R]QlQ RRUYU R\\ViV Rf[k[ RRFRW RRFYFYU R]F]Q RbHeK RcCbF RlSk[ R]FjFiN',
	    'PoUM]M R^UiU Rh[k[ RUE]E]N R`FjFjM RcHfK RiMfM RdCdF R`F`QlQlVk[ RUEUQSX',
	    'PoRE]E RfNjN R_UiU RRZ^Z Rf[j[ RRERZ RdHfK RdCcF RWSSW RTJWLXO\\U R\\HVU R`F`QlQk[ R`FjFjN',
	    'PoSE]E RfNjN RRO^O R_UiU RV[Z[ Rf[j[ RZEZ[ RdHgK RdCdF RXSRX RZMWT RTGRN RaFaQlQk[ RaFkFjN',
	    'PofNjN RXT\\T R`VhV RV[^[ Rf[j[ RVMVZ RaFkFkM RYE^J RdIgJ R^W^[ RWHRM ReCeF RYCVJ RUM]M\\T RaFaQlQk[',
	    'PoTL]L RfNjN RRS^S R_ViV Rf[k[ RUDUL RXOX[ R]I]L ReCdF RaFaQlQlY RcHfK R\\FTH RaFkFjN',
	    'PofNjN RbQlQ R`UjU Rf[k[ RSHSM RXCYNZY RSH^H^M RbFbQ ReHgK RWRS[ R^VYX RXMUX RlSk[ RXIVU ReCcFkFjN',
	    'PoRI^I RfNjN RaQlQ R_UiU RSY]Y Rf[k[ RSPS[ RXDXP RSP]P]Y RaFaQ RdIfK ReCdF RlSk[ RaFkFjN',
	    'PoRG^G RgNjN RSO^O RaQlQ RaUiU Rf[k[ RU[Y[ RYKY[ RaFaQ ReHgK R\\R]X RTSSY RfCfF RWDTO RaFkFjN RlRk[',
	    'PoYF_F RgNjN RTQ^Q R`UiU Rg[k[ RYCYK RTK^K^S RbFkFkM ReIgK RfCeF RbFbQlQlVk[ RTKTTSZ',
	    'PoRK]K RfNjN RaQlQ RRR]R R_UiU Rg[k[ RRERS RXEX[ RRE]E]S RaFaQ RaFkFkM ReIgK RfCeF RlSk[',
	    'Po`FkF RTJ\\J RfNjN RRN^N R_UiU Rf[j[ RTETJ RTE\\E\\J RdHgK RdCcF R[[W[ RUNTS\\S[[ R`F`QlQk[ RjGjN',
	    'PoRJlJ RcSfS RRXeX Rd[i[ RWNgNfS R_L\\N R\\P`R RVFhFhJ R`JeLlN RVFVJ R^JYLRN RWNWUjUi[ R_D^J',
	    'PoaFkF RfNjN R_UiU Rf[j[ RXKYN RWWZZ RXE^K RdHgK R[UXX RWGRL RdCdF RXCVI RTQ]QZV RaFaQlQk[ RjGjN',
	    'PofNjN RSN^N RaUiU Rf[k[ RSFSZ RXWYZ RdHgK ReCeF RVYRZ RZEYN[Y RaFkFjN RaFaQlQk[ R^ESF R^W^Z\\Y',
	    'PofNjN RUTZT R`UiU RW[\\[ Rf[j[ RULUV RULZLZT ReHgK RVCRL RbFbQlQk[ RUH^G^T\\[ ReCcFkFjN',
	    'PoeHiH RcRfR RRXeX Rd[i[ RaEiEiH RbEaKlJkK RXCSH RVF]FXK R^K\\N R[P`Q RWNfNfR RVHZJSM RWNWTjTi[',
	    'PocRgR RRWfW Re[k[ RSHSL RSHlHlL RUDXG R^C_G RiDeH R_J]L R\\OaP RWLWTlTk[ RWLhLgR',
	    'PoWN[N Ra[k[ RX[\\[ RTF\\F\\M RTFTQ]R]Z R^H^K RaLa[ R^HkHkK RWIYK RdDeF RlWk[ RjO`S RWCWF R[URV',
	    'Po`GgG RSG[G RaJjJ RTJ]J RRMlM RcTfT RRXeX Re[j[ RWPgPfT RXCTG RdCaG R[R`S RhF`J R\\FTJ RWPWUkUj[',
	    'PoRF\\F RcSfS RRXeX Rd[i[ RXCXM RTMWNfOfS RiLlKlM RbI^M R_IeK R[Q`R RVOVUjUi[ RcCbJ R\\HRI R^EhFiL',
	    'PoRE`E RfNjN RbQlQ RaUiU R\\[_[ Rg[k[ RSJS[ RXHXY R[J[Y RSJ_J_[ RbFbQ ReHgK RfCeF RXEWM RbFkFjM RlSk[',
	    'PoRK_K RgNjN RbQlQ R`UjU RSY]Y Rg[k[ RSQS[ RYEYQ RSQ]Q]Y RbFbQ RbFkFkN ReIhK RfCeF R]DRE RlSk[',
	    'PocFkF RXJ`J RfNjN RcQlQ RaUjU Rg[k[ RWGW[ R\\C\\[ RcFcQ ReIhK RfDfF R\\K_S RXDSN R\\JXV RlSk[ RkGjN',
	    'PoYE`E RYLaL RfNjN R`UjU RZ[][ Rg[k[ RVLV[ R^L^[ ReIhK RVERI RVLRP RfCeF RWIUN RbFbQlQlVk[ RbFkFjN',
	    'PofNjN RRO\\O RaQlQ R_UiU RY[\\[ Rf[k[ RUG]G][ RaFaQ RXIYL RdIgJ RXRYU RXCWF ReCeF RlSk[ RUGUTSZ RaFjFjN',
	    'PoaFjF RUL\\L RfNjN R`ViV RTY\\Y Rf[j[ RTPTZ RTP\\P\\Y RXD]I RdIgJ RWFRK ReCdF RXCVI RaFaQlQk[ RjGjN',
	    'PoRFlF RcSgS RRXfX Re[j[ R\\F\\L RcFcL RWNgNgS R_CaF RgHkL RXHTL R_L]N R[P`R RWNWUkUj[',
	    'PoXG_G RfNjN RaVjV Rf[k[ R\\G\\T RSDVF RRLVM ReHgK RVSR[ R`RXU RbFbQlQlVk[ ReCcFkFjN',
	    'PoSE_E RfNjN R`UiU Rf[j[ RXEXX RbFkFkM ReHgK ReCdF R]L_R RTLVS R]I[S RbFbQlQk[ RTIST R_WRY',
	    'PoRF_F RgNjN R`UiU RT[X[ Rg[k[ RSKSN RXQXZ RYCYK RSK^K^N RbFkFkM ReIgK RfCeF RTO]OXQ RbFbQlQlVk[ R^SRT',
	    'PoREaE RgNjN RbUiU RU[X[ R][`[ Rg[k[ RSIS[ RSIXIX[ R[I[[ R[I`I`[ RcFkFkM RfIhK RfCfF RUOVS R\\O^S RcFcQlQlVk[',
	    'PoSI\\I ReNjN RRQ]Q RRU]U R_UiU RX[][ Re[j[ RRMR[ RSESI RSE\\E\\I RRM]M][ RdHgK ReCdF RaFaQlQk[ RaFjFjN',
	    'PoUH^H RRN_N RfNjN R`UiU RTY^Y Rg[k[ RTRT[ RZCZN RTR^R^[ RdIgK ReCeF RUDSK RaFaQlQlVk[ RaFkFjN',
	    'PoRK`K RfNjN RaUiU RS[V[ Rg[k[ RVEV[ ReIgK R\\UXY RfCfF R^E^I R_NZX RYQRS RXDRE R\\D\\P[R]Z R`W^[_Z RbFbQlQlVk[ RbFkFjN',
	    'PoaFkF RfNjN R`UiU RUY^Y Rg[k[ RUQUZ RUQ^Q^Z RYJ_P R[D_H RdIgK RWERI RYIXMRR ReCeF RaFaQlQlVk[ RkGjN',
	    'PoTL\\L RfNjN RbRlR R`UjU R[[_[ Rg[k[ RRIR[ RYHYY RWE_E_[ RbFbR RRDUF RYL[R ReIgK RfCeF RYKTT RlRk[ RbFkFjN',
	    'PoSM^M RfNjN RaUjU R[X^X Rg[k[ RXHX[ RSH^H^L RUCVG RdHgK ReCeF RXPWTRX R\\CZH RSMSR^R]X RbFbQlQlVk[ RbFkFjN',
	    'PoTEYE RRJaJ RfNjN RWQ[Q RbQlQ RaUjU Rg[k[ RSOSY RWLWY R`X`[ RbFbQ R^E_G ReIgK RfCeF R[XRY R]C\\Q^Z RlSk[ RbFkFjN',
	    'PoSG_G RRL_L RfNjN RbQlQ RTU]U R_UiU RTZ]Z Rg[k[ RTPT[ RVCVL R\\C\\L RTP]P][ RbFbQ RbFkFkM ReIgK RfCeF RlSk[',
	    'PoRH_H RgNjN RSS^S R`UiU RSY^Y Rg[k[ RSMS[ RVDVK RYMYY R[D[K RSM^M^Z RbFkFkM ReIhK RfCeF RbFbQlQlVk[',
	    'PobFkF RSG_G RfNjN RTQ^Q RTT^T R`UjU Rf[j[ RTLTV RYJY[ RSM^M^T RZG_K ReIgK RWHRM ReCeF RYCUK R^YX[ RkGjN RbFbQlQk[',
	    'PoRI\\I RRM\\M RYS]S RSSWS R_UiU Rf[j[ RRDRM RSOSZ RYOYZ RRD\\D\\M RdHgK RdCcF RWYRZ R^XXZ RjNfN RaFaQlQk[ RaFjFjN',
	    'PoSJ^J RfNjN RSO^O RaQlQ RRU^U R`UiU RgZkZ RSFSN RXFXN RZQZ[ RSF^F^N RaFaQ RdHfK RXCWF RWOST ReCeF RlRkZ RaFkFjN',
	    'PoSKXK R[K`K RcQlQ RSQXQ R[Q`Q RbUjU RU[X[ Rg[k[ RSDXDXZ RcFcQ ReIhK RfCfF RcFkFkNgN R[D`D`[][ RlSk[ RSDSUR[ R[D[UZ[',
	    'PofNjN RSTWT RZT^T R`UjU RSY^Y Rf[j[ RSOS[ RZO^O^[ RaFkFkM ReIgK ReCdF RWDTK RVG^F[K RXNRO RaFaQlQk[',
	    'PoSF_F RTM^M RfNjN RaQlQ R`UjU RU[X[ Rg[k[ RTITM RYSY[ RTI^I^M RaFaQ RaFkFkM RXCYF ReIgK RfCeF RSP^PXS RlSk[ RYURV R[UTV R^UZU',
	    'PocFkF RTF_F RTN`N RcQlQ RVR^R RbUjU Rg[k[ RYDYF RVJ^J^R RcFcQ RfIhK RfCfF R\\U^Z RZHZSVZ RgNjNgN RkGjN RlSk[ RTFTSR[ RZNXW',
	    'PofNjN R\\U^U RaUjU RSGSJ RZLZZ R^L^T RSG_G_J RbFkFkM RXCYF ReHgK ReCdF RTRWSTZ R_XYZ RUJRR RXLTM R^LZL RXMVW RbFbQlQj[g[',
	    'PoSJ`J RTN_N RcQlQ RbUiU Rg[k[ RZCZ[ RTF_F_M R_P_[ RcFcQ RfIhK RfCfF RcFkFkNgN R\\R\\X RWRVY RlSk[ RTPTUS[',
	    'PoRIYI R\\KaK RdQlQ R[RaR RcUjU RSWYW Rh[k[ R^[a[ RSPSY RVCVO RSPYPYW R\\EaEa[ RdFdQ RfIhJ RgCfF RjNgN RdFkFjN RlRk[ R\\E\\SZZ',
	    'PoTH^H RTL^L RfNjN R`UiU RTX[X Rf[j[ RTSTX RTETL RTE^E^L RbFkFkM RYR[U ReHgK RVNRR ReCdF RXRWV R][Y[ RbFbQlQk[ RUP^P^U][',
	    'PoSJWJ RZJ_J RSM^M RfNjN RRQ_Q RaUiU RX[\\[ Rg[k[ RSESJ RSEWEXJ RZEZJ RZE_E_J ReIgK RfCeF RVQUT]U][ RbFkFjN RbFbQlQlVk[',
	    'PofNjN RaQlQ RTR\\R R`UiU RTU\\U RY[\\[ Rg[k[ RRKRN RTNT[ RTHYHYK RTN\\N\\Z RTD]D]K RTDSK_K_N RaFaQ RaFkFkM RdIgK ReCeF RlSk[',
	    'PoRH^H RcSgS RRXfX Re[j[ RYDYN R`E`J RWOgOgS RXH\\K RfIjM ReIaM RkEiI RXGSM R^M\\O R\\QaS ReDfJ RWOWUkUj[ R\\CTE',
	    'PoRI`I RfNjN RcQlQ RbUjU Rg[k[ RcFcQ ReIhK RVDWG RVKSQ RXNTX R^D[I R]KZQ R`NZY RfCfF RXQRQ R_PZQ RXUXYRY R_U`YZY RcFkFjN RlSk[',
	    'PoTJ_J RfNjN RVQ^Q RaUiU RVU^U RVZ^Z Rf[j[ RVMV[ RYEYJ RVM^M_[ RTE_E_J RbFkFkM ReHhK ReCdF RbFbQlQk[ RTETTR[',
	    'PobFlF RRXfX Rd[i[ RYHYM RVMXNgOfS RRH^H]K RcD_H RbFfKlM R[Q`S RiGfJ`M RcSfScR RTD]DYF RXHVJRL RVF]H RVOVUjUi[',
	    'PoRE_E RTL^L RcQlQ RTT]T RaUiU Rg[k[ RROR[ RTHTL RYTYZ RTH^H^L RRO_O_[ RcFcQ RVPWS ReIhK R[PZT RjNgN R[[_[[[ RfCdFkFjN RlSk[',
	    'PoTN]N RfNjN RRS^S R`UiU RSZ]Z Rf[j[ RSUSZ RYNYZ R]U][ ReHgK RSGTK RXFYJ RTLRQ R^F\\K RbFbQlQk[ ReCcFkFjN R^DRE',
	    'PoTM^M RfNjN RbRlR R`VjV RT[W[ Rg[k[ RROWOW[ RbFbR RZQ\\T RSQUS R[J_M R[D_H ReIgK RWDSH RYGTL RfDfG RWURW R^UYW RYO^O^[[[ RlSk[ RbFkGjN',
	    'PofNjN RSU^U RaUjU RSZ^Z Rg[j[ RSESM RSPS[ RYPYZ RSP^P_[ ReHhK RXDRE RWKRM RWHXN RZE`E_M]M R\\E[L RbFbQlQk[ ReCcFkFjN',
	    'PofNjN RbQlQ RUR^R RaViV RUV^V Rg[k[ RZ[^[ RUNU[ RUN^N^[ RbFbQ R[J_N RYI[J ReIhK RfCeF R\\H`J RSDVF RYIUMRN RVIRJ R_E\\F RYCXK RlRk[ RbFkFjN',
	    'PoRJ_J RfNjN R`UiU Rf[j[ RTQTZ RWQWY RZQZY RbFkFkM RUDVG R[K^O ReHgK R]D[G RVKRO ReCdF RbFbQlQk[ RTQ]Q^XRZ',
	    'PoRGaG RgNjN RRNaN RcQlQ RaUiU Rg[k[ RWGW[ R\\F\\[ RSK_K_S RcFcQ RcFkFkM RSS]S`W RUDWF ReIhK RfCfF RUVRY RWQTW RlSk[',
	    'PoTM_M RfNjN RUQ^Q RbQlQ RUU^U RaVjV RUZ_Z Rf[k[ RSGSJ RULU[ R[K[Z RSF_F_I RbFbQ RdIgK RVKRO ReDeF RYDTN RlSk[ RbFkFjN',
	    'PoSD_D RVG]G RiGlG RaIjI RSM_M RdTgT RRXfX Re[j[ RSDSM RVPgPgS RcDiDiH RcEaG RUI[I^K R[QaS RYJUL RdKlN RZFYJ RhL`N RVPVUkUj[',
	    'PodFkF RbUjU Rf[j[ RTDTM RTDYDYM R\\D\\M R\\DaDaM R_ObP RWNYP RfIiK RgCfF RXQVV RTPRP RjNgN RUWaZ RSS`T\\XXZS[ RkGjN RdFdQlQk[ RVGUO R^G]O',
	    'PoTGaG RVM`M RcQlQ RXS^S RbUiU Rg[k[ RXIXS RYVZZ R\\V]Z R^I^S RcFcQ ReIhK RfCfF R_V`Y RZC[F RVVV[ RcFkFkNgN RlSk[ RTGTSSZ',
	    'PogNjN RaUiU Rg[k[ RRDXDXL RZD_D_L RcFkFkM RTFUH R[G]G ReIhK RfCfF R_IZK RYNRQ RZQST R\\TUW RXIRK RXN`P R^VYYR[ RcFcQlQlVk[',
	    'PoSF_F RfNjN RUP]P RUS]S R`UiU RV[Y[ Rf[j[ RULUS RYSY[ RSM^L^S RTHVJ R\\U_X R\\J_M ReHgK RXHSM R^H\\J RXH]J RYDVK RUUSY RbFbQlQk[ ReCcFkFjN',
	    'PoTH`H RUK_K RfNjN RUO_O RUS`S RaUjU Rg[k[ RUFUT RXVXZ R[H[S RZD[F ReHgK R[V\\Y RTVS[ R^V_Y RUDUK RbFbQlQlVk[ ReCcFkFjN',
	    'PoRE^E R`FlF RTI]I RfLlL RcSfS RRXeX Rd[i[ RTGTH]G]I RWOgOfS RlJlL R[K]L RhDkE RUKSM RfHfL R[Q`S RcK_L ReDcJ RYIXMVM RWOWUkUj[',
	    'PofNjN RaUjU RWX\\X Rg[j[ R\\[_[ RSOS[ RWTWY RWT\\T\\X RSO_O_[ RZR]S ReHhK RRI_I]K RWQUS RTE]EWKRM RVGZHZNWN RbFbQlQk[ ReCcFkFjN',
	    'PoSG\\G RXJ]J RaMjM RRXeX Rd[i[ RSDSG RTITM RSD]D]G RaJaM RaJjJjM R_M\\O R\\Q`S RWOfOfScS RdD^F RbEfHlI RbEjEeG_I RWOWUjUi[ R]LVM RXGYMSM',
	    'PoTI_I RSL`L RfNjN RbQlQ RbUiU Rf[k[ RTDTI RVVV[ RXDXI R\\D\\I RTO_O_S RTD_D_I RbFbQ RbFkFkM RYS_Z ReIhK RTOTS_T]W RfCeF RXTRW RZZUZ RlRk[',
	    'PoRF_F RVM[M RfNjN RSO^O RbRlR RTU]U RaViV RTX]X Rg[k[ RSISO RTRTX RVKVL[K[M RTR]R]X RSI^I^O RbFbQ RXCYF ReIgK RfCeF RbFkFjN RlRk[ R]ZXZR[ R[ZU[',
	    'PoTFlF R_KjK R_MjM R_OkO ReThT RVYgY Re[j[ RZHZP R_H_P ReGeO RYRhRhT R[GVL R`G\\K R\\SbT RYRYVkVj[ RTFTSS[',
	    'Po^EkE R_KkK R_MjM RWNZN R_OjO R_RkR RTF[F[M RTFTQ\\Q\\Y R`I`S RbCbG ReIeR RhChG RVIYK R_I]L RWCWF R`UcXgZl[ R[[X[ R^UjUgXcZ^[ RZURV',
	    'PoRF`F RUM^M RfNjN RSP_P RbQlQ RaUiU Rg[k[ RWCWH R\\C\\H RSJSK_J_L RbFbQ R\\U_X ReHhK R^R\\U RfCeF R[PSS RYTSW R[VRZ RXR[U[Y RZ[W[ RlRk[ RbFkFjN',
	    'PoRE_E RbFkF RTLXL R[L_L RUP_P RUS_S RaViV RUV_V RUY_Y Rf[j[ RTITL RVOV[ RVCVG RTIXIXL R[I[L R[O[Y R\\C\\G R[I_I_L RdIgJ RUORS ReCfF RjNfN RbFbQlQk[ RjGjN',
	    'PoTD`D RcFkF RUJXJ R\\J`J RTMXM R\\M`M RfNjN RRRYR R[SaS RbUjU R[VaV R[ZaZ Rg[k[ RVOV[ RZDZM R[P[[ RSGSIaGaJ R[PaPa[ RcFcQlQlY RVSYV RfIhK RfCfF RVQSX RkGjN',
	    'Po^FlF RRZjZ RRJR[ R^C^J RRJjJj[ R\\K_N R^VaZ RTPWS RePhS RWN^QbS RdL^RdU R]SUW',
	    'PoWF]F RRY[Y RRJR[ RWCWJ RRJ[J[Y RVLWN RWUXW RcKfN RdDlL RSMWRSV RcF]L RVPYV R_QjQe[ RYMVT RdCbH',
	    'PoWF^F RbLhL RfXjX RRY\\Y RRJR[ RWCWJ RRJ\\J\\Z RcPc[ R_PjPjX RTN[U RWUYW RgGlL RWRSV RcG^L RfChH R[Q]R RYMWS RdCbI',
	    'PoWF^F RaJiJ R_TkT RRY\\Y RRJR[ RWCWI RRJ\\J\\Z R_N_T R_NkNkT RfVj[ RTNZU RWUYW ReClJ RdV_Z RdD^J RWQSV ReJeTgY ReSbX RYMVS RePeW',
	    'PoWF]F R^HlH R`LeL RaVeV RRY[Y RRJR[ RWCWJ RRJ[J[Z RaOaW RaOeOeV RYP[R RTNXRYU RRPTR RVUXW RiDkF RhUcZ RjNfX RXMUU RkMhV RlVk[ RgCgMiZ R^H^T][',
	    'PoWF]F R_GlG R_KkK R^OlO RbTkT RRY\\Y R`ZlZ RRJR[ RWCWJ RRJ\\J\\Z ReGeO RfTfZ RTN[U RWUYW RaCbF RiCgG RXQSV RVLWM RYMVS RaO^Z',
	    'PoWF\\F R]HkH R]OlO R]SjS RRY[Y RRJR[ RWCWJ RRJ[J[Z RbDb[ RfEf[ R]KjKjS RTNXRYU RVUXW ReSlY R`V\\Y RULWM RXMUU RaR_X RjDeD RdD^E',
	    'PoWF^F RaJhJ R_ScS RfSkS RRY\\Y RRJR[ RWCWJ RRJ\\J\\Z R_M_S R_McMcS RfMfS RfMkMkS RhWl[ RaWcY RTNZU RWUYW ReClJ RdD^J RWQSV RYMVS RaT^[ RhTfZd[',
	    'PoSE^E RaFlF RfHkH RSM]M RSQ^Q R`RkR RR[l[ RSESR RVTV[ R\\T\\[ RSH]H]M R`J`R RbTb[ RfEfJ RVThTh[ R`JkJkR RcMhO RgLcP RbC_J RYMYQ RYEYI',
	    'PoWF]F R_GlG R`KkK R^PlP RaUkU RRY[Y R_[l[ RRJR[ RWCWJ RRJ\\J\\[ RfTf[ RbCcF RbS^X RTMYW RYLTW RdM`V RiCgGbT',
	    'PoUFlF RUPkP RYU`U Rc[l[ RYQY[ R\\G\\P R`C`F RcQc[ RcGcP RUJkJkP RlXl[ RkTbV RUFURSZ R_ZV[',
	    'PoUElE RULjL RYO`O RcSlS R[[j[ RYMYS R[T[[ R\\E\\L RcMcS RcEcL RUHjHkL RjXi[ RjNbP RUEURSZ R_RWS RgUXW',
	    'PoTJlJ RURjR RYU`U RYRYZ R[J[R RUNjNjR RcJc[lYl[ RXFcF`I R[HcJ RjUcV RZCTJUSSZ R\\ZV[ R`YYZ',
	    'PoUFkF RULiL RYO`O RbRkR Re[l[ RYMYR R[F[L R_C_F RcMcR RbFbL RYUeUeZ RUHiHiL RlXl[ RjNbO RZUXYR[ RUFTOSU R_RWS',
	    'PoUEkE RUMjM RYP`P RbTkT RYMYT R\\E\\M R]W^[ R_C_F RcMcT RUIjIjM RhVl[ RbVdZ RYWW[ RcEcKbQ RjPbQ RUEURSY R_SXT',
	    'PobLfL RTNkN RTTiT RYW`W RY[_[ RV[][ RYTY[ R[N[T RTQiQiT R\\EeElH RcNc[kZk[ RXHgHfK RYERH R]JUL RjVbW RTNUTSZ',
	    'PoUElE RYM`M RVP^P RcPlP RWWbW ReXhX RX[k[ RXTX[ R\\E\\J R_C_F RWTbTbW RcEcO RUHjHjK RlXk[ RYJYPTU RWRiRhX RTJjKbM RUEUKSQ',
	    'PoTElE RYM`M RbPkP RYUhU RYWhW RUZlZ RYKYP RYRYZ R[E[K RcEcP RYRhRhZ RTHjHjK R_CaE RTETSS[ RTKjLbN R`OXP',
	    'PoUFlF RYN`N RcQlQ RYQ`Q RVQ^Q RVVkV RYLYQ R\\F\\K R_C_F RXSaS`[ RcFcK RcLcQ RUHjHjK RaVWZ RaVgYlZ RUFURS[ RUKjLcN RgR`S',
	    'PoTElE RTLkL RYN`N RcRkR RWR^R RWTjT RXWiW RU[l[ RYLYR R\\E\\K R`C`F R`S`[ RcLcQ RTHjHkL RcEcJbO RiNbN RTETSS[',
	    'PoTE^E RaEkE RSH^H R`HlH RULkL RTSjS RYV`V Rc[k[ RYSYZ RYEYK R\\L\\S RcScZ RcLcS RfEfK RUOjOjS RkXkZ R_JaL RkUbV R`YXZ RULTTSZ',
	    'PoSGkG RUMkM RTSjS RXV_V Rb[k[ RXTX[ R\\M\\S R_C_J RcTc[ RcMcS RUPjPjS RlXl[ RXDZF RfDcG R_GdIlK R_GYIRK RjVbW RUMTTSZ R`ZX[',
	    'PoTFkF RYN_N RTVkV RYLYQ R[F[K R_C_F RVQ[Q`R_[ RTHiHiK RYS[T RfSdT R_UfYlZ R_UXYRZ RTLiLbO RcFcQkPkQ RTFTMSS',
	    'PoUElE RYL`L RbNlN RVN`N RZSgS RRWlW RYJYN RZPYS R[E[J RcEcN RZPgPgS RVQVUhUhW RUGjGjJ RUEUJSP RTJiKbL R_WdZl[ R\\ZR[',
	    'PoUFlF RYM`M RcPlP RXRjR RYKXP R\\F\\K R`W`[ RcFcP RUHjHjK R`C`F RXVeUhV RfWf[kZ RaRXV RZWZZV[ RUKjLcN RUFUQSY R`OXP',
	    'PoUEkE RYL`L RcOkO RYO`O RVO]O RXQkQ R[SiS RXVkV R\\ZbZ RYJYO R[E[J R\\V\\Z R_C_E RcEcN RUHjHiJ RiWdX RaWfYl[ RUJiKbL RUEURSY RXRWZ R_ZX[',
	    'PoUElE RYL`L RcOlO RVO^O RVSlS RWUhU RZZjZ R[V[[ R[E[J RcEcN RYJYO\\PhQhU RUGiGiJ RYWjWj[ R]Q[UYXUZ RUJjKbM RUEUPSX',
	    'PoUElE RcNkN RWQlQ RYZjZ RYWY[ RYJXO R\\E\\J RcEcN RYWjWj[ RUHjHjJ RUJjKbL RZQ_TkV RUETRSZ RYL`MXO RhQcTVV',
	    'PoTElE RXL_L RXO`O RbOkO RVT]T RaTiT RVWiW RVRVW R[E[J R_C_E RcEcO RaRiRiW RTHjHjJ RbWb[lYl[ RTJiKbM R\\WYZR[ RTETKSP RXJWO]PUR',
	    'PoSF^F R`GlG RbLiL RSO^O RbPiP RVTYT R_UlU RZZ^Z RVQVZ RWHWO RYCYF RZHZO R[Q[Z RSJ^J^O RbCbT RiCiT RhWl[ R]SZU RcV^[ RYYUY RSFSUR[',
	    'PoSE^E R_ElE RULkL RTSjS RYV`V RYZ_Z RV[\\[ Rc[l[ RXCXJ RYSYZ R\\L\\S RcLc[ ReCeJ RlXk[ RVE]H ReDgGlI ReDcG_H RXDUHRI RUOjOjS RjUbV RULTTSZ',
	    'PoTElE RXL`L RcOlO RXO`O RYQaQ R_TlT RYZiZ RXJXO RYWY[ R\\E\\J RcEcN RePeT RYWiWi[ RTGjHjJ R[QVR ReQkR R`SWU RTJjKbL R[SeT RTETRR[',
	    'PoUElE RYL`L RWO_O RXUhU RUZjZ RUQU[ RYJYO R\\E\\J RYTaS_Y RUGjHjJ RUQjQj[ R_UfY R_UXX RTJjKbM RcEcOlNlO RUETJSP ReS_S',
	    'PoUElE RUJjJ RYL`L RcOlO RYO`O RVO^O RVQlQ RYVhV R][`[ RYSXV RYJYO R\\E\\J R_C`E R`V`[ RdEdN RYShShV RUHjHjJ R[XU[ ReXlZ RjLcM RUEURSY',
	    'PoSD^D RaDkD RTMkM RTSjS RYV_V Rb[l[ RYSYZ R\\M\\S RUG]G]K RbGbK RcSc[ RcMcS RbGjGjK RTOjPjS RlXk[ RXIYJ ReIfJ RUGUK RjUbV RTMTTSZ R\\ZU[ R_ZX[',
	    'PoUEkE RYL`L RbNkN RVO_O RVRkR RYXgX RYZgZ RYJXO RYUY[ R[E[J RcEcN RWVgVg[ RUGiGiJ RWPaP`SlU R`R[TUV RUJiJbL RUETPSX RgO`P',
	    'PoUElE RcNlN RXRlR RZTbT RZYbY RYJXO R\\E\\J RZVbVbY RdEdN RUHjHjJ RlXk[ RfWi[ ReYa[ RiTdZ RZVZZ RdPfX RgPjP RUJjKcL RUEUSSZ RXRWZ RYL`MXO',
	    'PoTElE RXL`L RcOlO RXO`O RWS_S RbSjS RaVjV RWV_V RWY_Y RWQW[ RXJYO R\\E\\J R_Q_S RcEcN RTGjHiJ RbQjQjT RcWk[ R\\Q_Q RZQWQ RiWeY_[ RTJjKbL RTETRR[ R\\QZQ',
	    'PoTElE RYL`L RVO_O RaTiT RWT]T RaViV R`YiY RX[\\[ RYJYO R[E[J RWTVW]W\\[ RVQ]Q]T RaQaY R_QjQi[ RTHjHjJ RkXgY RTJjKbM RcEcOlNlO RTETQSZ',
	    'PoTElE RXL`L RXN`N RcNlN RWPjP RXUiU RVWkW RXRXU RXJXO R`U`[ RcEcN RcPcU RXRiRiU RTGjGjJ R]P^U R[E\\J RbXk[ R_XV[ RTJjKbL RTETQR[',
	    'PoUElE RYL`L RVN`N RbNlN R`RlR RUS]S RUU]U RZ[][ Rc[h[ RUQUW RYJYP R[E[J RUQ]Q][ RcEcN RhPh[ RUGjGjJ RbUdX RRX]WRZ RUEUJSP RTJiKbL',
	    'PoTEkE RbMkM RVMZNjO RSQkQ RVWhW RRYlY R_W_[ RcEcL RTIjHiI RYO[Q ReOcQ ReUhU RUGXG[F\\H_GcGfGjG RVSWVZUVU R_U\\U RXIXL\\K`K R`UcU RZU\\U_U RXGTFSN R_G[GXG RfGbG_G RVShShVeUbU_U',
	    'PoTF_GlH RTO]O R`QdQ ReQlQ RVTYT RdWlW RZZ]Z RVQVZ RWHWO RYCYF RZHZO R[Q[Z RTJ]J]O ReCeN RfQfW RiNi[ R_SbV RaDbG ReGkM RjDhF R]SZU RcR`Z ReG`N^U RYYUY RTFSUR[',
	    'PoTElE RYK`K RcMlM RVNkN RVTkT RUZlZ RXJXM RcJbM RYOhOhS RYViViY RaOaRdQ R\\MXM RTGjGiIfI RaVaXdW RUIXITI R_M[M RYOYR[RhReQ RYVYXZXhXeW RcEcIfI R\\E\\I_I RTETRR[ R[IXI RcI_I RhKbK RaQ]Q RaW]W',
	    'PoTElE RTIjI RYK`K RcKiK RcMlM RXM`M RWOkO RXZhZ RcEcM ReRdU RYWhVh[ RTGjGjI RgQkU RcQaS RZQVU R\\E\\I RXIXN RYVXZ RgTdT RWQ]Q\\UZT RjQdR RTETRR[',
	    'PoTElE RXL`L RcNlN RXN\\OjP RYU^U RcUhU R^ZcZ RfZkZ RWZ\\Z RWRWU RWXW[ RXJXO RWW\\W\\[ R^X^[ RaPaU RcEcN R^WcWc[ RfXf[ RTGjGjJ RfWkWk[ RWRkRkU R[E\\J RTJjKbL RTETRR[',
	    'PoSF\\F R_HcH RfHkH R^JlJ RSO\\O RUTXT RVHVO RYHYO RSJ\\J\\O R_D_H RaPaY R_DcDcH R^PePd[ RfDkDkH RWCXF RhSkZ RgQiY RjSg[ RZQ[YXY R`MdMdP RgOkOhY RfEfH RhLfT RZT\\TYT RdR`S RUQVYTY RdU`U ReX_Y RSFSVR[ R^J^T][',
	    'PoVEjE RVJiJ RYK_K RWN_N RaNjN RTP_P R`PlP RTV^V R`UkU RVXYX RZ[^[ Rg[l[ RVUV[ RWPWU R[P[[ R]E]I RTS^S^U RbIbN RbUb[ RcEcI RdPdU RfNfP RhPh[ RVGiGiI R`SkSkV R^WZX RkWgX RYZTZ RbXeXbX RfZa[ RYIYMYP RhKaL RVEUM RTPTVS[ R`P`V_[',
	    'PoSFkF R_C_O RXJZL RfIcL RXISN R_JcOlS R\\Uh[ ReJiL R_JZORR R\\RTU R[ReRaW\\YT[',
	    'PoTFjF RUJiJ RRNlN R_C_M RZQRU R\\NXR\\VaYj[ RXRgRcU]XS[',
	    'PoRF^F RbHlH Rb[l[ RYCYO RlWk[ RWV[Y R\\I^K RXJTO RVQRU RbMiMfQ RXL^N RdSaZ RUGSL R\\GZK RdC`K RVR\\R[UWYR[',
	    'PoSF_F RbQfQ R^[l[ RYCYO RfCfQ RUIWJ R\\I_K R\\G[K RWPRU RiGlL RYL^N RUGSK RYJVNRO RVR]R[UXYR[ RjNhSeV_X RbG`M RVRWV[Ye[',
	    'PoSF_F R`HkH R`NlN R^[l[ RYCYO RUIWJ RgTkX R\\I_K RdR_W R\\G[K RWPRU ReCeOiV RYL^N ReNbU RUGSK RYJVNRO RVR]R[UXYR[ RVRWV[Ye[ ReJdS',
	    'Po`ElE RSF^F ReXjX R_[l[ RYCYO ReEeP ReJkJkU RXK\\M R\\I^K RXKTO RWQRU RUGSL R\\G[K RVR]R[UXYS[ RVRXV[Ye[ RaIaRkPjX',
	    'Po`ElE RRF^F RfKkK Rf[j[ RXCXO RfEfQ RYV\\Y R[I^K RXKTO RVQRU RXL^N RUGSK R\\GZK RUR\\RYW RVUZUWYS[ RbIbQlQj[',
	    'PoSE^E RYCYO RfCfN R`HkGjK RdTjZ RWT\\Y RUTXU R\\I^K RXKTO RWPRU RbNeW RXL^N RUGSK R\\G[K RVR]R[UWYR[ R`NkNhUcY`Z R`G`Q_Y',
	    'PoSE_E RaPgP RgSjS RbXkX R_[l[ RYCYO RbJbW RaJgJgP RbGkGkQ R[M^O RXK\\M R\\I^K RkUkX RXKTO RWQRU RUGSL R\\G[K RcC`J RVR]R[UXYS[ RVRXV[Ye[',
	    'PoSE_E R`IkI R_PlP RdZkZ RaZiZ R_ZfZ RYCYO RXU]Z RUSXV R\\I^K RXKTO RXPRU RiTl[ RXL^N RUGSL R\\G[K RVR]R[UWYR[ ReCeObZ',
	    'PoRE^E R_PkP R_WkW R^[l[ RXCXO R_H_X RcCcW RgCgW R_HkHkX R[I]K RXKSO RVQRU RXL^N RUGSL R\\GZK RUR\\RZVVYR[ RURWVZYd[',
	    'PoSE^E RaIkI R_IiI RaNjN R_SlS R^[l[ RYCYO ReJeY R[M^O RXK\\M R\\I^K RhEkI RXKTO RVQRU RUGSL R\\GZK RcCaI RaK`Q RVR\\R[UWYR[ RVRWV[Ye[',
	    'PoRF^FlG R_PkP R_UkU RYCYO R_K_[ ReCe[ RWV[Y R\\I^K RhDkE RVQRU RXL^N RUGSL R\\GZK RXJUNRO RVR\\R[UWYR[ R_KkKk[h[',
	    'PoSE^E RaIjI RaMjM RaRjR Rg[l[ RXCXO RaDaQ RgRgZ RaDjDjQ RlWl[ RWU[Y R[I]K RbW_[ RXJTN R[GZJ RVQRU RXK]N RUGSK RVR\\RZUWYR[ RcRbX',
	    'PoSE^E R_GkG RYCYO ReCe[ RWU\\Z RUSXV R\\I^K RfSlX RaMdOhT RiMlP RdR^X RXKTO RWPRU RXL^N RiJgP RUGSK R\\G[K RbJ_Q ReObU RVR]R[UWYR[',
	    'PoRF^F R_OhO RfXjX R^[l[ RXCXO RdIdW R[I]K RcOhT RbQ^V RXKSO RVQRU R`JaM RXL^N RgJfM RUGSL R\\GZK RaC^J RdMbS RUR\\RZVVYR[ RURWVZYd[ R`GkGkSjX',
	    'Po_ElE RRF^F R`VkV R^[l[ RXCXO R`I`X RdIdV RhIhV R`IkIkX R[I]K RXKSO RVQRU RXL^N RUGSL R\\GZK RUR\\RZVVYR[ ReEdL RdMhMcM RdQhRcR RURWVZYd[',
	    'PoSF]F R_FlF R`NkN Ri[l[ RXCXO R_R_[ R`H`N RkHkN R_RlRl[ RWU[Y R[I]K RfJbL RhHdK RXJTN R[GZK RVQRU ReCfF RXL\\N RcIhK RfNbW RUGSL RVR\\RZVWYR[ RhTiWbW',
	    'PoSF\\F RaHdH RgHjH RaJdJ RfJjJ R_MkM R_OlO R`VkV R^[l[ RXCXO R_D_L R`R`V RaEcDbL ReCeL RgEiDhL R`RkRkV RkDkL RcWcY RVT[X R[I]K RZUUZ RXKSP RVQRU RWL\\N RhWg[ RUGSL R[GZK RUR\\RYW',
	    'PoSF^F R`HjH RTI^I RSM_M R`NlN R^[l[ RYCYM RgSlX RdS_X RVPRU ReCeOiV ReObV RWMTS RVQ]Q[UWYR[ RVQXV\\Ye[ ReKdS',
	    'PoSF]F RSI\\I RSM^M R^OiO R^[l[ RdIdW RcOhT RbQ^V R`JaM RgJfM RXCXMTT RaC^J RdNaS RUQ\\QZVVYR[ RjXfX RVQXV[Yd[ R`GkGkSjY',
	    'Po_EkE RSF]F RSI]I RSM^M R`WkW R^[l[ R`I`X RcIcV RgIgV R_IkIkW RVPRT RXCXMTS RVQ\\QZVWYR[ RdEcL RcMgMcM RcRgRcR RVQXV[Yd[',
	    'PoTGkG RVM`M RbMkM R[H[[ R`C`G RfHf[ R[N`S RcS_W RfLkV R[MWV RfLbU RTGTSSZ',
	    'PoTFlF RWI_I RbIkI RiPlP RW[k[ R[G[P R_K_O R`C`F RfGfP RjKjP RhXk[ ReWhX R_Q[U RdS`W RYT^W R_X[Z RXLWQ RbKbP RTFTSS[',
	    'PoTFlF RWJ`J RbJkJ RZZjZ RYZhZ R[G[P R^L^O R`C`F RfGfP RfUk[ R`V\\Z RbLbP R^Q\\TVV RXLWQ RjLiPlOlP RTFTSS[',
	    'PoTFlF RWI`I RbIkI RiOlO RcTkT R`ThT R]TeT R[UbU RWU`U RbWjW R`WhW RYXbX RVX`X R[G[O R^K^O R`C`F RfGfO RXR`R`[kZl[ RbKbO RiKiO RXKWP RTFTSS[ RgP]R',
	    'PoUFlF RWIaI RbIlI RYSiS RXZkZ RXVX[ R\\G\\M R`C`F R`S_V RgGgM RYOiOiS RXVkVk[ RgHlM RgHbM R\\IWM RZI`L RYOYS RUFUSSZ',
	    'PoTFlF RWI`I RaIkI RYS_S RYV_V RYQYY R[H[O RVQ`Q_[ ReHeO R[I^M ReUk[ RhLjN R`CbF ReIaN RiUdZ R\\IWO RdQgX ReKiL RbQjQgX RTFTSS[ R_XWY RaXZY',
	    'PoUEkE RWI_I RaIkI RVRkR RYXgX RYZgZ RYVY[ R\\G\\N RfGfN RYVgVg[ RhKkL ReHaM R\\IWN R[J_K RdIiK RXPaP`SlU R`Q\\TUU RUETRS[ RfO`P',
	    'PoUFlF RXNjN RWPlP R[O[R R\\G\\L R`C`F RaMaP RfGfL RgOgR RXRiRiX R]HXJ RWH]H_K RbHhHlK RgHaK ReZl[ RiTfT R\\ZV[ R[TXT\\T RcXfX R^X[X RXRYXZWiWeX RUFURSZ RaT^TaTdTaT R]XaX^X RaXdXaX',
	    'PoTFkF RZIdI RRLlL RVSiS RVWiW RVOVW RZCZI R_L_W ReCeI RVOiOiW RdXl[ RZXR[',
	    'PoTFjF RRJlJ RVRiR RVViV RVNVV RZCZJ R_J_V RdCdJ RVNiNiV RcXl[ R[XR[',
	    'PoSF_F RVJ\\J RRL_L RbMiM RSS^S RSW^W RSOSV RVCVJ RYLYV R\\C\\J RSO^O^V R[X^Z RiIlM RUXR[ RcJ_M RfCjK ReCbL R`QjQg[',
	    'PoRF_F RVJ\\J RRL_L RTS^S RTV^V Rf[l[ RTOTV RVDVI RYLYV R\\D\\I RTO^O^V RfCfZ RaHkHkL RlWl[ R\\X^Z RVXR[ ReRaZ RfMcX RaHaL ReJdV',
	    'PoRG_G RfJlJ RRK_K RTR^R RTV^V RaZkZ RTNTU RVDVJ RYKYU R[D[J RTN^N^U RaQa[ RfCfQ RaQkQk[ R[W^Z RVWS[',
	    'PoRF_F R`IlI RVJ\\J RRL`L RaQkQ RTS^S RTV^V R`ZlZ RTOTV RVDVJ RYLYV R\\D\\J RTO^O^V RfCfI RfKfZ R\\X^Z RVXS[',
	    'PoUKiK RRNlN RVTiT RVWiW RRGRJ RVQVW RZIZN R_N_W RdIdN RVQiQiW RRGlGlJ RVDXG R^C`G RhDeG R[YR[ RcYl[',
	    'PoSG^G RaHkH RRK_K R`NlN RTR^R R`TkT RTV^V R_ZlZ RTNTV RVDVJ RYKYV R[D[J RTN^N^V RfCfN RfOfZ R[X^[ RVXS[',
	    'Po^FlF RbIhI R^KlK RRM]M R_QjQ R_TjT RZZkZ RXCXM RZMZZ R_M_T RbCbH ReKeT RhChH R_MjMjT RgVjW RaV]X R\\EZJ RSETK RUMURTZ',
	    'PoUFZF RdFiF RVIZI RdIhI RUMiM RZOdO RRQlQ RVVhV RSKSN RVDVK RZKZO R_Q_X ReKeO RVShShX RdDiDiK RSKkKkN R\\HaH\\I R]DaD\\F RZDUD RVSVXfYl[ R[ZR[',
	    'PoSIkI R[[_[ R_Q_[ RWS[U RfRcU R^IjN RUE_E_NkS R_ISN R^ORT RaXjZ R\\XTZ RgD_E',
	    'PoRH^H RfOjO R[[_[ RYDYO R_Q_[ RYS[U ReScU RYHRN RWHYJ]L R`NlT RaWiZ RbC_J RfFcM R^M\\PRT R]WTZ RaGkGjO R\\CRE',
	    'PoRH_H ReIlI RaZkZ RaQa[ ReCeQ RaQkQk[ RYDYM^R RUSWU R]S[U RYGSM RYMSS RYI_L R[W^Y RWXRY RYRY[V[ R]CTE',
	    'Po^FlF RSH]H R`NkN RV[Y[ Rh[l[ RYQY[ R_R_[ R`H`N ReCeF RkHkN R_RlRl[ RZN]Q RZW]X RUSVU R\\S[U RfJaL RhHdL RXHSM RYDYNTR RXI^K RbIhL RWWRX ReNbW RgTiWaW R\\DTD',
	    'PoULiL RUPiP RRTlT RUDUL R\\W\\[ R_D_T RUDiDiL RhWl[ RUXR[ RdFcJ RbWc[ RYF[J',
	    'PoUIiI RUMiM RUQjQ RRUlU RUDUM R[W[[ R_D_U RUDiDiM RhWl[ RUXR[ RbWc[',
	    'PoSL^L RSP_P RXT_T RST]T RSDSL RVGVJ RWWX[ RYDYT R[W[Z RSD^D^L RlVlZ RhEkH R^V_Y RSWR[ R\\G[K ReDeKgW RiK`L RlKcL',
	    'PoSM]M RbMiM RSP]P RRT[T RSDSL RUGVJ RVVWZ RXDXT RSD]D]L R\\V^Y RhHlL RbI^M RYVZZ RfChI RZGZK ReCbJ R`QiQg[ RSWR[',
	    'PoSM]M RSP^P RWT^T RRT\\T Rf[l[ RSDSL RUGUJ RVWWZ RXDXT RSD]D]L R_I_L ReCfNfZ R_IkIkL RlWl[ R\\V]X RcW^[ RYV[Z RdQaY R[GZK RSWS[ ReJcV',
	    'PoaEkE RSM]M R_NlN RSP^P RST\\T Rf[l[ RSDSL RVGVJ RWVWZ RXDXS RSD]D]L RaEaN RgNgZ RlWl[ R\\V]X RYVZY RZGZK RgNcU`Y[[ RSWS[ RhIdT RhEfR',
	    'Po_GlG RSL]L RSP^P R`SkS RRT[T Rf[l[ RSDSL RUGUJ RWWWZ RYDYT RZWZZ RSD]D]L R`K`S RfCf[ RkKkV RlXl[ R]V^Y R[GZK RSWR[',
	    'Po`KkK RTL^L RSP^P RWT^T RST\\T RTDTL RVGVJ RWWWZ RYDYT RZW[Z RTD^D^L RgEjH R^W]Y RfQjZ RdR`[ RTVRZ R[G[K ReCeNhX ReMbX ReIgU ReJcV',
	    'PoSH^H R`JkJ RSL^L RSP^P RSDSL R[W[[ RSD^D^L RhWkZ RhDkG RUXR[ RbWc[ RYEYS RST\\S ReDeJdP_T ReJgPkT',
	    'Po_IlI RSM\\M RSP]P RaQiQ RVT]T RRT\\T RaZiZ RSDSL RUGUJ RXDXT RYVYZ RSD]D]L RaCa[ RiCi[ R\\V]Y RZGZK RSWR[ RVWVZ',
	    'Po`FlF R]FiF RZGgG RUNiN RURiR RTTiT RRWlW RUKUQ RWEWJ R[Y[[ R_K_W RUKiKiQ RhXk[ RVXS[ RlHlJ RbYb[ RXCVFRH RcDiD R`EcIjJ',
	    'PoSM^M RaNlN RSP^P RWT_T RRT\\T RaZlZ RSDSL RVGVJ RVWWZ RXDXT RSD^D^L RaQa[ RaFaN RfCfZ RlFlN RlQl[ R\\V_X RZV[Z R[GZK RSWS[',
	    'PoSM\\M RSP\\P RVT]T RST[T Rg[k[ RSDSL RUGUJ RVVVZ RXDXT RZGZK RSD\\D\\L R\\V]Y R^JbN RYVYZ RhCgRcZ RdI^V RSVR[ RaD_K RcQeV]V ReKlKlVk[ RhKfV',
	    'PoeJlJ RSM]M RSP^P RVT^T RST\\T RaZkZ RSDSL RVGVJ RVVWZ RXDXT RSD]D]L RaPa[ ReCeP RaPkPk[ R]V^Y RYV[Z R[GZK RSWS[',
	    'PocGjG RSM]M RSP^P RWT_T RRT\\T RSDSL RVGVJ RVWWZ RXDXT RSD^D^L R\\V^X RgN`T RkSeY ReC_J RZV[Z RaHfJ RdQlQiU R[GZK RjHeM`O RdTgW_[ RSWS[',
	    'Po`HlH RSM^M RaNkN RSP^P RST\\T RbZjZ RSDSL RUGVJ RWWW[ RYDYT RZV[Z RSD^D^L RbRb[ RfCfN RbRjRk[ R^V^Y RSWR[ R[G[K R^SZT',
	    'PoTElE RWIkI RWMlM RYQiQ RYTjT RWVkV RZLZQ RaMaV RiLiQ RiXl[ R]N^O RdXe[ RYXX[ RfNdP R^X^[ RaIjM ReGhH RaF]JWM RTETSS[',
	    'Po_IkI RSM\\M RSP\\P RbRjR RST\\T RSDSL RVWVZ RXDXT RYVYZ RSD\\D\\L RhKlO RiFkJ RgEjH RcK^O RcP^U RdD_I RUGUK R\\V\\Y RbTfYl[ RZGYK RSWR[ RiSeX^[',
	    'PoaGkG RSM\\M RSP\\P R^QlQ RTT\\T R`XlX Re[i[ RSDSL RUGUJ RVWVZ RXDXT RYVYZ RSD\\D\\L RcSfU RdLfN R\\V\\Y RSWR[ RbC^K RYGYK RaKjJjUi[ RaJaP`W',
	    'Po^GkG RSM]M RSP]P R`QjQ RRT[T R`UjU Re[l[ RSDSL RUGUJ RVWVZ RXDXT RSD]D]L R`L`W ReKe[ R_MkMkU RlXk[ RgIlN RbI^N R\\U]Y RYVZZ RZGZJ RdCaL RSWR[ R]SYS',
	    'Po_GlG RSM]M RSP^P RaQjQ RVT^T RRT[T Rb[e[ RSDSL RUGVJ RVVWZ RXDXT R[GZK RSD]D]L RaKaQ ReCeG ReQe[ RaKjKjQ R]V^X RYV[Z RiTlY RaT_Y RSWR[',
	    'PoSL\\L RSP\\P RUT\\T RRTZT RhTkT Rf[l[ RSDSL RSWR[ RUGUJ RVWVZ RXDXT RYVYZ RSD\\D\\L R^F^J RgKg[ RfKkKkT R^FkFkJ RlXl[ RdCeF R\\V\\Y RZGYK R_QbS_Z R`I^Q R`LdLbW',
	    'PoRG^G RURiR RUTjT RRWlW RUNUR RYDYL R[X[[ R_N_W RUNiNiR RhXk[ RYO[P RbXc[ RWG\\I RfGaL RVXS[ RdOcP RXFTK RbE_H RkLgL RaFlFkL R\\CTD',
	    'PoXKfK RUQiQ RTTjT RRWlW RSFSI RUMUQ R[Y[[ R_C_F R_M_V RXHXJfHfK RUMiMiQ RSFkFkI RiXk[ RXDZF RVXS[ RdOcP RfDdF RbYb[',
	    'Po`FjF RSM]M RSP]P R_TlT RRT\\T RSDSM RUGVK RVWW[ RXDXT RSD]D]M ReCeJ R`PcQ RgWl[ ReV`[ R\\V]Y RZWZZ R[GZK RSWR[ R_JkJjN RbMeMfQeV',
	    'Po`HjH R^LkL RSM\\M RSP\\P RRT\\T RSDSL RUGVK RXDXT RYVZZ RSD\\D\\L RgLkP RgEjI RdK_Q ReOaS ReD`H R\\V\\Y RZGZK RSWR[ ReHbN RfT`V RjUhW`[ RVWVZ',
	    'Po_ElE RSM\\M R_OkO RSP]P RST\\T R_TkT R^ZlZ RSDSL RUGUJ RVWW[ RXDXT RZWZZ RSD\\D\\L R_I_O RcEcO ReQeZ RgEgO R_IkIkO R\\V]Y RZGZK RSWR[',
	    'Po_HlH RaLeL RSL\\L RSP\\P RUT\\T RSTZT RSDSL RUGUJ RWDWT RYGYK RSD\\D\\L RaOaW RiDkF RgVcZ R[V\\Y RYVYZ RjOeY RVVVZ RjMgV RlWk[ RSWR[ RaOeOeVaV RgCgPiZ R_H_T][',
	    'PoSL\\L RSP\\P R`PjP RRT[T R]UlU RSDSL RUGUJ RVVWZ RXDXT RSD\\D\\L R`L`Q RhSh[ R`LjLjQ ReCjH RaX^[ RdC_I R\\U]Y RYVZY RaHhHlI RZGYJ RSWR[ RbSaY',
	    'Po^FlF RaJiJ RSM\\M RaNiN RSP\\P R]RlR RRT\\T RgVlV R_ZlZ RSDSL RUGUJ RWDWT RYGYK RSD\\D\\L R_R_[ RaCaR RhRhV RiCiR R[V\\Y RYVYZ RcRbV RVWVZ RSWR[',
	    'Po`FlF RSM]M R_MlM RSP^P RRT\\T RaUjU RaZjZ RSDSL RVGVJ RVWWZ RXDXT RSD]D]L RaQa[ RbHcL ReCeG RaQjQj[ R]V^Y RYV[Z RhHhL R[GZK RSWR[',
	    'Po_HlH RSM\\M RbNjN RSP\\P RbQjQ RbTjT RST\\T R_VlV RSDSL RUGUJ RVWVZ RWDWT RSD\\D\\L R_L_V R`C`H RbKbT RbKjKjT R\\U]Y RYVZZ RgXl[ RZGZJ RbY][ RSWR[ RiD_F',
	    'Po^FiF R\\JlJ R^RiR R]TjT R\\WkW RV[Z[ RTEZEZ[ R^L^R RbXb[ RcMcW R]NjMjR RiXl[ RVIXK RVPXR RfIlN RhDkG R^Y\\[ R_D\\G RaI[N ReXf[ RfFhK RcC_L RTETSS[',
	    'Po^HkH RSM\\M RSP\\P RUT\\T RRTZT RSDSL RUGUJ RWDWT RYGYK RSD\\D\\L RdKjQ RgNkMkN RhEkI ReK^Q R[V\\Y RYVYZ RhIhN R`J^N RcCaH RdQ_S RfT`W RjUhW`[ RVWVZ RSWR[ RcM]M',
	    'PoSD^D RVG]G RiGlG RaIjI RSM^M RVSiS RUUiU RRWlW RSDSM RVOVS R[Y[[ R_O_W RVOhOhS RcDiDiG RhXk[ RVXS[ RcEaG RbYc[ RUI[I^K RYFYJUL RdKkM RgK`M',
	    'PoZHcH RYJdJ RZQcQ RZTcT RYVdV RVKV[ RZDZH RZMZQ R\\X\\Z R^C^H R^M^V RZMcMcQ RcDcH RhUc[ RWDRH RVLRP R`X_Z RgRkZ RjPfX RWHUM RZXY[ RaN`P RfGfNiX R\\N\\P RfJhW RfIkIiV RgCeN',
	    'Po_IlI RgLkL RSM\\M RbNjN RSP\\P R_QlQ RbSjS RUT\\T RSTZT RbUjU RaZkZ RSDSL RUGUJ RWDWT RYGYK RSD\\D\\L RaWa[ RhIhL RaWkWk[ RaEhEfH R[U\\X RYVYZ RdIbM RVWVZ RSWR[ RbC_I_S][',
	    'PoTDkD RaIkI RVTiT RUViV RRXlX RVQVT RWKWO RWFWI RWF_F_I R_Q_X RVQhQhT RhZk[ RgKkO RhGjG RVZS[ ReKbO RdRcT RZR[T RWL_L_N\\N RfFgM RYHWH RZH\\G RTDTKSQ R[NYN',
	    'Po^ElE R_HkH RSM\\M RfMkM R_OkO RSP\\P R_SjS RUT\\T RSTZT R_UjU R_WjW RSDSL RUGUJ RWDWT RYGYK RSD\\D\\L R_J_O R_Q_W ReCeH RfJfM R_QjQjW R_JkJkO RdJaM R[V\\Y RYVYZ RgYl[ RbY\\[ RVWVZ RSWR[',
	    'PodEjE RaKgK RSM\\M RdMlM RSP\\P RaQjQ RUT]T RaTjT RSTZT R_[l[ RSDSL RUGUJ RWDWT RYGYK RSD\\D\\L RaOaT RdVd[ ReCeM RfOfT RgVh[ RaOjOkT R[V\\X RXVZY R_HlHkJ RVWV[ RaV`[ RaVkVk[ RSWR[ R_H_S]Z',
	    'PoRHlH Rd[i[ RVMV[ R\\C\\H R_H_[ RbCbH RdPdX RUNjMi[ RVDXG RhDgG RdKlO RYKRN RZQYY',
	    'PoRI_I Re[i[ R[[^[ RTOT[ RVQVX RWCXIY[ R[C[I R[Q[X RTO^O^[ R\\K^M RUKRN RTDTG RdEhElM R]D]G RcF`M ReNcX RaNjNjVi[',
	    'PoRI_I R`JlJ R[[^[ RSOS[ RVQVX RWCXIY[ R[C[I R[Q[X RSO^O^[ RhWl[ R\\K_M RhDkG RhWc[ RUKRN RTDTG RdCdPiY RjQfY R^D]G RcJgW RjMgW RdKbX',
	    'Po`GlG RRI_I RaPlP RaUlU Ri[k[ R[[^[ RTOT[ RVQVX RWCWI RYIY[ R[C[I R[Q[X RTO^O^[ RaKa[ RfCf[ RaKlKl[ R\\K_M RUKRN RTDTG RhDkE R^D]G',
	    'PoSJ[J R`JiJ RRM[M RRR[R R`RjR RRW[W R`WjW R\\[k[ RRMRY RSESJ R\\E\\[ R`E`Y RSEiEiJ R`MjMjW RlXl[',
	    'PoTJgJ RRRiR RRViV R^[k[ RRNRX RTETJ R^J^[ RTEgEgJ RRNiNiV RkXk[',
	    'PoUDiD RRGlG RbKkK RTP]P RaPiP RTS]S RTV]V RaVjV RTX]X RaXjX RTSTY RTNUP RaNa[ RTNiNiP RaSjSjX RbGbK RYKRL R]N][lZl[',
	    'PoUEiE RSHkH RbLkL RTQ]Q RaQiQ RTS]S RTV]V RaVjV RTY]Y RaYjY RTSTY RTNTQ R_C_H RaNaY RTNiNiQ RaSjSjY RbHbK RYKRM R]N][kZl[',
	    'PoUEiE RRHlH RTP\\P RaPiP RSS\\S RSU\\U RaUjU RSX\\X RaXjX R][l[ RSSSX RTMTP R]M][ R_C_H RaMa[ RTMiMiP RaSjSjX RVKfJiK R\\HUK',
	    'PoUGiG RUIiI RRLkL RTQ\\Q RaQiQ RSV\\V RaVjV RSX\\X RaXjX RSSSZ RTNTQ RUDUI RaNa[ RUDiDiI RTNiNiQ RaSjSkX R]N][lZl[ RYSSS',
	    'PoUEiE RRHkH RTJiJ RRMlM RaQjQ RSV\\V RaVjV RSY\\Y RaYjY R][l[ RSTSZ RTOTR]P][ R_C_G R_I_M RaOa[ RTOjOiR RaTjTjY RZSST',
	    'PoVFhF RRIlI RaQjQ RTT]T RTV]V RaVjV RTY]Y RaYjY R][k[ RTTTZ RUOUR]P^[ R_C_N RaOaY RUOiOiR RaTjTjY RWDTG R_HeKlM R_HYKRM',
	    'PoTF^F RRI_I RaLjL RTR\\R RaRiR RTT\\T RTV\\V RaVjV RTY\\Y RaYjY R][k[ RTTTY RTOUR R]O][ RaOa[ RaEaL RTOiOiR RaEjEjL RaTjTkYl[ RUCTG RXJ_M RXKRN RYFXK',
	    'Po_IdI RTL[L R_MdM RTP[P R_RdR RgRkR RRT]T R_WdW Rd[l[ RRESHTP RTH[H[P RRD]D]H R_M_W R_E_I RdEd[ RgEg[ RlYl[ RXRXT RYW\\Z RVWR[ R_EkEkIfJ RgMkMkWfW',
	    'PoTE^E RbGkG RRG_G RSI^I RaQiQ RTT]T RTV]V RaVjV RTY]Y RaYjY R][k[ RTTTZ RYCYI RUOTR]P][ RVL]L\\N RaOaY RTOiOiR RaTjTjY RWKSN RbGfLlN RcCaH RiHeK`N',
	    'PoUQ]Q RaQjQ RTV]V RaVjV RTY]Y RaYjY RSGSM RTSTZ RUNUQ RYCYL RSG^G^M RaNaY RUNiNiQ RaSjSkY R[I[K R\\D_F RVDRF RcGfJlM RVIUL RcCbGkFgJaM R]N][lZl[ RYSTS',
	    'PoTF]F R`FjF RUJhJ RULhL RRNlN RaRiR RTT]T RTW]W RaWjW RTY]Y R][l[ RTTTZ RUHUL RUPTR]Q][ RTDTE]D]F R_H_L RUHhHhL RTPiPiS R`D`EjDjF RaTjTjY RaPaYkYl[',
	    'Po`EkE RTG]G R_IlI R`KkK RUQ]Q RaQiQ RTS]S RTV]V RaVjV RTX]X RaXjX R][l[ RTSTY RUNUQ RTD]D]G R]N][ RUJ]J]M RaNaY ReIeM RUMYMiNiQ RaSjSkX RbEcI RhEhI RTFSN',
	    'PoUDiD RRGlG RbKlK RUPhP RTUiU RTYiY RTSTX RUMUP RbGbK RUMhMhP RTSiSiX R_P_[lZk[ RYKRL',
	    'PoUGiG RUJiJ RRKlK RUPhP RTViV RTYiY RTSTY RUNUQ RUDUI RUNhNhQ RUDiDiI RTSiSiY R_P_[kZl[',
	    'Po`FjF RULhL RRNlN RURhR RTViV RTYiY RTTTY RTDTE]D]F RUPUQgPgR RTTiTiY R`D`EjDjF R_R_[lZk[ ReJiJ RUHVKYJUJ RTFYGhHhKeJ RXJ[J_I_K[JXJ RbJ_JbJ',
	    'PoYGeG RYKeK RYNeN RbQjQ RRT\\T RbTlT RYDYN RTFTQ\\Q\\[ RbQb[ RYDeDeN RiTi[ RjFjP RUTTZ',
	    'PoYJfJ RYMfM RYPfP RbSjS RRV\\V RSESH RYHYP RUJUS\\S\\[ RbSb[ RYHfHfP RjJjS RbVjVj[ RSElElH RVVTZ',
	    'PoYLeL RYOeO RYQeQ RbTjT RRW\\W RbWlW RYIYQ RUMUT\\T\\[ RbTb[ RWIeIeQ RhWh[ RjLjT RVXT[ R\\EYHSK RTEdEcGkGjJgJ',
	    'Po`EhE RYJgJ RYMgM RYOgO RcRlR RRU]U RYHYO RUFUR]R][ R`C`G RcRc[ RYHgHgO RlFlR RcUlUl[ RVXR[ RVUUZ',
	    'PoSEkE RXMfM RXPfP RXRfR RbTjT RRW\\W RbWlW RXKXR RTMTT\\T\\[ R`C`I RbTb[ RXKfKfR RhWh[ RjMjT RVYR[ RcG^G RiFbG RSH`I\\J',
	    'PoSJ^J RTS]S RTMTS RXCXJ RTM]M]S ReCeN RUUVX RaOdUhYl[ R[TZY R_NjNhTcX^[ R^YSZ RSF^F RbIjI',
	    'PoRF^F ReHkH RSJ^J RTS]S RTMTS RXCXJ RTM]M]S ReCeN RUUVX RaOcUhYl[ R_NjNhTcX^[ R[T[YRZ',
	    'PoRE^E R`FlF RTH]H RSP\\P R[[f[ RX[d[ RYCYG RTJ]J]M RfCfI RVMVO RbJeMkP RTJTL\\L[PTT R`IjIgM`P R\\XbX RYReR_UWW R[SbVkW R`URX',
	    'PoUEiE RRIlI RRM^M R`OlO RSP^P R`SjS RTU]U RTRTU RYLYP RYGYK RTR]R]U R_C_G ReGeK ReLeS RVWVY RbTfYl[ RjTfX_[ RYZRZ R[V[YVZ',
	    'PoSN^N R_NlN RSQ]Q R_SjS RTV]V RWZ]Z RU[[[ RR[Y[ RTSTV RYLYP RTS]S]V ReLeR RbCbJkIkK RVWV[ RVD[E RaTeXl[ R[WZZ RjTfX_[ RbGiH RhDcE R[ISK R[GTH R\\C\\I',
	    'PoSE_E R`ElE RTG^G RTK^K RSOYO RUUjU RbWjW RYCYG RUPUX\\W\\[ RUS]Q]S RTITJ^I^K RbXb[ RfCfH RbPbSjQj[ RbIeLjN RaIjIgLbN R[NUN R^NXN',
	    'PoRE^E R`EkE RTG^G RVShS RVUhU RRXlX RVPVU RYCYG RTITK]J]L RbUb[ ReCeH RSOXOhPhU R[KZN RbIdLjN RZI^I R`IjIeMlN RWITI RgL`N R_P_T]VUX RVLVN RYIWI R]NWN',
	    'PoRE^E R`ElE RSG^G RTK]K RRNXN[OiP RWRhR RWThT RRVlV RW[^[ RT[[[ RWPW[ RYCYG RTITJ]I]K RfCfH RUKVN ReLlN RcKjN R`HjHgKdM_N RiWbX R_WdYl[ R`Z[Z',
	    'PoRE^E R_FlF RTG]G RVN\\N RVQ]Q RcQjQ R\\TlT RWZgZ RWVW[ RYCYG RTITK]J]K RcOcT RfCfI RWVgVg[ RcKjO RXPRR RVLVN RZI]I RWITI R`IjIhKdM_N R\\RXTRV RROXNXP RWIYIWI RXSbT',
	    'PoRE^E R`ElE R`HkH RSJ]J RVM^M RRMWNiO RVXiX RS[k[ RVUVX RVJVM R\\JZM RSHSI]H]K R_S_Z RfCfH RRTWThUhY RaJlL RXCYG\\G RhJ`L RUO^QgS RXGUGRG RZQkS RfP^RUT',
	    'PodFiF R[HiH RdKiK RSP^P R`QlQ RSR]R RTW]W RWZ]Z RR[Y[ RYOYR RTTTU]T]W R[D[K`K`N R[F`E`F RdKdN RdDdF ReOeT RiDiN RTGWH RVXVZ RUDXE RWJTN RbUeXk[ RZLXM R`TkTgXdZ^[',
	    'PoRE^E R`ElE RXVfV RUYiY RR[l[ RXCYG RTITK]J]K R_O_Q R_V_[ RfCfH RXTXUgTgV RSQSSkQkT RXOZQ RfOdQ RbHcJ R`HjHhKaN ReLlN RSG\\H]I RWITI RXIZI RXNRN R[MTN R^MXN',
	    'PoSE^E R`FkF RSG^G RWN^N RXXfX RS[k[ RYCYG RTITK]J]K ReCeH RXUXWfUfX RaOdS RVRYS RfObQ RdXb[ RVLWN RZX[[ RbIdLjN R[SbRkU R`HjHgLaN RSNXO]P[RSV RiQeS RZI]I RWITI RXI[I RYIWI',
	    'PoUI\\I RbIiI RUMiM RUEUM RVOVZ R_O_Z RaEiEiM Ri[lZl[ RaUeV RYQ\\R RXU[V RbPeR RhOhTiZ R[YRZ RfY\\Z R]DUE',
	    'PoVNgN RUThT R^I^[ RUDWH RgDfGdI RlWk[ R]C^G RVIhIhTiZ RVIVSTY',
	    'PoSIWI RZI^I RSM^M Re[h[ RSESM RTOTZ RYOYZ RZE^E^M R`V`X RZQ\\R RZU\\V RUTWV RcEhEkL RUQWR RcF_M RWYRZ RWCSE R]XWZ RdNcX R]P^X RaNjNjVi[',
	    'PoSIWI RYI^I R`KkK RSM^M RTETM RXOX[ RYE^E^M R`Y`[ RZQ\\R RZU\\V RUQWR RUTWV RiEjH RgSjZ R\\ZWZ RXDSE RfCfNiX RTOUZRZ RfMcX R^O^V^[ RfJgU',
	    'PoSIWI RYI]I RSM]M RTETL RXNXZ RYE]E]L R]O^X_[ RfUl[ RYQ[R RYT[V RUQVR RUTVV RfUa[ RbMgW RdCfG RhOdX R[YVZ RWDSE RbIeT R_IkIfV RSNTZRZ R\\T][',
	    'PoSIWI RYI]I R_IlI RSM]M RaNjN RTETM RXOX[ R]E]M R_Y_[ RZQ[R RYU[V RUQVR RUTVV RgDjG RgVcZ RbCbIbOeUiYl[ RjPfX R[YVZ RWDSE RYE]E RSOTZRZ R]O]V][ RbI`V',
	    'PoTIWI RZI^I RfMjM RTM^M RaVjV Rd[k[ RTETM RTOTZ RYOYZ RZE^E^M RaOaV RaOjOjV RZU]V RURWR RUUWV RlXk[ RZR]R ReEcL`M RWYRZ RXDSE R]YWZ R^P_X RaZi[ RaEkEjL',
	    'PoSIWI RYI]I RSM]M Rb[k[ RSESM RXOX[ RXE]E]M R]O]W^Z^[ R`H_K RbLbZ R_HlHlK RYU[V RUTVV RlWkZ RdCeF RYQ[R RSOTZRZ RkObS R[YWZ RWDRE',
	    'PoSIWI RYI]I RSM]M RSESM RSOS[ RXOX[ R]E]M R]O^Y_[ RZU[V RUTVV RdKjR RbGeN RdRhT RaWiZ RcC`K RbGjGhKcP_R RVZR[ R[ZWZ RWDRE RYE]E R]T][',
	    'PoSIWI RYI]I RSM]M RbThT Re[j[ RSESM RSOS[ RXOX[ RYE]E]M R^O_Y_[ RbMbV RbMhMhT RZU[V RUTVV RZQ[R RUQWR RVZRZ R[YWZ RWDRE RcCaL R]T^[ RbHlHlUk[',
	    'PoTIWI RZI^I RbJkJ RTM^M RbOkO R`VlV Rd[k[ RTETM RTOTZ RYOYZ RZE^E^M RfDfV RZU]V RURWR RUUWV RlXk[ RZR]R RWYR[ RXDSE RcEaM R^YWZ R^P_X RaZi[',
	    'Po`ElE RTIWI RZI^I RTM^M RcVjV Rd[k[ RTETM RTOTZ RYOYZ RZE^E^M RcJcW RbMjMjW RZU]V RURWR RUUWV RkXl[ RZR]R RdJ`R RWZRZ RXDSE RdEaP R]YWZ R^P_X RaZi[',
	    'PoSIWI RZI^I RSM^M RaOkO RYU\\U RaVkV Rc[k[ RSESM RSOSZ RYOYZ RZE^E^M RaHaW RfCfV RaHkHkW RkXl[ RWYRZ RWDSE RVRSR RYR\\RYR RTUVVSV R]YWZ R]P^X RaZi[',
	    'Po`GjG RSIWI RYI]I R_LlL RSM]M R_QlQ Rd[h[ RSESM RSOS[ RXOX[ RYE]E]M R]O^Y_[ ReCeL RhLh[ RbTdX RZU[V RUTVV RYQ[R RTQVR RVZRZ RWDRE R[YWZ R]S^[',
	    'PoSHVH RSM\\M RdNkN RdWlW Re[l[ RSESM RSOS[ RXE\\E\\M R\\O\\W]Z RhEhW R`VdZ R^RbX RWOXZVZ RVDRE RkDdF R[S\\Z R^EbE^MbM`X R[HXI',
	    'PoaEkE RZI^I RTM^M R`MlM RbVkV Rd[k[ RTETM RTOTZ RYPYZ RZE^E^M RbPbV RaHjHjM RbPkPkV RZU]V RURWR RUUWV RkXl[ RWYRZ RXDSE ReEcM RWISI R]YWZ RZR]Q_X RaZi[',
	    'PoSIWI RZI^I RbJkJ RSM^M RbPkP RbUkU RSDSM RSOS[ RXOX[ RZE^E^M RbEbU RbEkEkU RZQ\\R RZU\\V RUQVR RUTVV RhWl[ ReWa[ R^O_Y_[ R[YWZ RWDRD RVZRZ',
	    'Po`FkF RSIWI RYI]I RaIkI R`MlM RSM]M RaTkT Rg[k[ RSESM RTOTZ RXOX[ R]E]M R_Y_[ RaOa[ RfCfL RfOfT RaOkOk[ RZQ[R RZU[V RUQWR RUTWV RVYRZ R[YWZ RWDSE RYE]E R]O]V^[',
	    'PoSIWI RYI]I R`IkI RSM]M RaPkP RaSkS R_WlW RSESM RTOTZ RXOX[ RYE]E]M R_Y_[ R`D`H RaMaS ReGe[ RaMkMkS R`DlDlH RZQ[R RZU[V RUQWR RUTWV RVYRZ R[YWZ RWDSE R]O]V^[',
	    'Po`HjH RSIVI RXI\\I R`LjL RSM\\M R`PjP R_TlT RSESL RSNS[ RXNXZ R]O]Y RXE\\E\\L R`E`O R`EjEjO RfVjZ RYT[V RUTVV RdW`[ ReSgX RgRjR RYQ[R RZYWZ RWDRE ReQcX RVZRZ',
	    'Po`ElE RYI]I RcIjI RcLjL RSM]M RbOlO R`WlW Rc[k[ RSESM RSOSZ RXOXZ RYE]E]M R`E`W RcGcL RcGjGjL RYU\\V RUUWV RlXk[ RdQkT RURWR RfLcR RWYRZ RiQfTbT RWDSE R]YWZ RZQ\\Q^X RSIWI R`Zh[',
	    'PoSIWI RYI]I RSM]M R_RlR RTETM RSOS[ RXOXZ RYE]E]M R]O^V_Y R`G`J R`GkGkJ RgVl[ RYT[V RUQVR RUTVV RhNiP RgJkM ReCfF RcJ`M ReQiY RfRbZ RVYRZ R[YWZ RWDSE RZQ\\R]X ReMdX',
	    'PogHkH RSIVI RXI\\I RSM\\M ReOjO RgRkR Ri[l[ RSESM R\\O\\Z RXE\\E\\M RgFgK RgLgR RfTjTkYl[ RYUZV RcKlKkM RWNXZVZ RWDRE RfTeZ RSOTZRZ R`F`T_[ RcKcTbZ RkD`E',
	    'PoSM^M R`PkP R`RlR Rd[k[ RSESM RTOTZ RYPYZ R^E^M RZU]V RUUWV ReRjW RiMlP RiElH ReIbK RhJcO ReFfH RaKdL RZR]R RURWR RbE`I RWYRZ RcU_W RXDSF RZE^E RZI^I ReOdU RWISI R]YWZ R^P_X RaZi[ RjDaE',
	    'Po^HlH RSM\\M R^OlO R_SkS RSESM RXOXZ RYE\\E\\M RcDc[ RgEg[ R_KkKkS RYQ[R RYU[V RUQVR RUTVV RhVlY RaV]Z RgRjX RSOUZR[ RcR`X R[YW[ RWCSE ReD`E RSHWISI RkDgD RYI\\IYI R]O\\S]X',
	    'PoaHkH RZI^I RaKkK RTM^M R`QlQ Rd[k[ RTETM RTOTZ RYPYZ RZE^E^M RaDaK RfKfN RaDkDkK R`N`OkNkP RZU]V RURWR RUUWV RkXl[ RbTjX RdObU RWYRZ RiSfVbX RXDSE RWISI R]YWZ RZR]Q_X RaZi[',
	    'PoSIWI RYI]I RaIkI RSM]M RaMkM R`UdU RgUlU R`ZdZ RgZlZ RSESM RXOX[ RYE]E]M R]O]Z R`P`[ RaEaM R`PcPbZ RePe[ RfEfM RgPg[ RgPkPiZ RaEkEkM RlPl[ RZQ[R RYU[V RUQVR RUUVV RSOUZR[ R[ZWZ RWDSE',
	    'PoWHgH RWJgJ RWMgM RTQjQ RTTjT RRWlW RTOTT RWEWL R_O_T ReTe[ RWEgEgL RTOjOjT R`C]E RYTYYS[',
	    'PoUH_H RUJ_J RUL_L RSQaQ RSSaS RRUbU RSNSS RUEUL RZNZS R]U][ RUE_E_L RSNaNaS ReFeU RlCl[ RWUUZR[ RlTcU',
	    'PoTH^H RTJ^J RTL^L RSQ_Q RSS_S RRU`U Rh[l[ RSNSS RTETL RYNYS RYCXE R\\U\\[ RTE^E]L RSN_N_S R`IhIiZ RlWl[ RcDcS_[ RVUTZR[ RcMaX',
	    'Po`ElE RTG]G RTJ]J RTL]L R`NlN RSQ^Q RSS^S RRU^U RSNSS RTETL RXNXS R[U[[ RTE]E]L RSN^N^S RfEf[ RVUTZ',
	    'Po`ElE RTH^H RTJ^J RTL^L RSQ_Q RSS_S RRU`U Rh[l[ RSNSS RTETL RYNYS R\\U\\[ RTE^E^L RSN_N_S RhGhZ RlWl[ RVUTZR[ RcGcQbX',
	    'Po`ElE RTG^G RTJ^J RTL^L RSQ_Q RSS_S RRU`U Ri[l[ RSNSS RTETL RYNYS R\\U\\[ RTE^E^L RSN_N_S RcQcZ RcLcV RcJcS RcGcQ RcEcO RcKiKj[ RlWl[ RWUUZR[ ReOfU RgYaZ',
	    'PoTH^H RTJ^J RTL^L RSQ_Q RSS_S RbTgT RRU`U Re[j[ RSNSS RTETL RYNYS R\\U\\[ RTE^E^L RSN_N_S RbLbV RbLgLgT RdC`K RWUTZR[ RbHlGlRj[',
	    'PoTG]G RTJ]J R_JlJ RTL]L R`OjO RSQ]Q RSS]S RRU^U RSNSS RTETL RXNXS R[U[[ RTE]E]L RSN^N^S R`FjFjO R`QbS ReSjX ReS`X RVUTZ ReCe[a[ RiRgTcT',
	    'PoTH]H RbHkH RTJ]J RbLkL RTL]L RSQ^Q RSS^S RRU_U RaXjX RSNSS RTETL RXNYS R[U[[ RTE]E]L RSN^N^S RaRaY RbDkDkL RbPlPlZ RfSiV RfQdU RbDbLaQ RVVTZR[ Rk[h[',
	    'Po_GlG RTH]H RTJ]J RTL]L RSQ^Q RaSiS RSS^S RRU_U RaWiW R^ZlZ RSNSS RTETL RXNXS R[U[[ RTE]E]L RSN^N^S RaOaZ ReCeM RaOjOjZ ReGkM RdH_M RVVTZ',
	    'PoTH]H RaJkJ RTJ]J RTL]L RaMkM RaQkQ RSQ_Q RSS_S R`TlT RRU_U RSNSS RTETL RYNYS R\\U\\[ RTE^E^L RSN_N_S RaFaQ RaFkFkQ RgWj[ RhRjS RXCYF ReCeF RfShY RVUTZR[ ReWbZ_[ ReQdX',
	    'PoTH]H RTJ]J RTL]L RaNjN RSQ^Q RSS^S RaUkU RRU_U RSNSS RTETL RYNYS R[U[[ RTE]E]L RSN^N^S RaJaN RfQfU RaJjJjN R`FbI ReFgH RiFkI RkDiF RbC`F RgDdF RaQkQkU RVUTZR[ RaQaZkYl[',
	    'PoeEkE RTH]H RTJ]J RTL]L ReNkN RSQ^Q RSS^S RcTjT RRU_U RcWjW Ra[l[ RSNSS RTETL RXNYS R[U[[ RTE]E]L RSN^N^S RcPcZ ReCeH RfIfN RcPjPjZ R`HlHkK RVVTZ R`H`U_[ RiKbK',
	    'PoTG[G R^HkH RTJ[J R^LkL RTL[L R^NkN RSQ\\Q R`QjQ RSS\\S R^TlT RRU\\U RSNSS RTETL RWNWS RYUY[ RTE[E[L RSN\\N\\S R^E^L RaTa[ RbCbL RgCgL R^EkEkL ReVjZ ReTgX RVUTZ RkVgW ReZ`Z R^N^V][',
	    'Po^DlD RTG[G R_IjI RTJ[J RTL[L R_NcN RfNjN R^QkQ RSQ\\Q R_SjS RSS\\S R]VlV RRU\\U RSNSS RTETL RWNWS RYUY[ RTE[E[L RSN\\N\\S RbObU R_L_McLcN RdCdJ RgOgU R`F_HjFjI RfLfMjLjO R]J]LlJlM RiWhX ReVhYl[ RaWa[ RVUTZ RdU]X ReZ`Z',
	    'PoRFkF RVWhW R_J_O R_C_F RdIdO RWRhQh[ R\\H^I RhKlO RhIjN RbG`I RVITN RZOWO RTZR[ RjHcI RWRUY RSI[IZO RhNbO',
	    'PoRFlF RVUhU R_T_[ R_J_O R_C_F RdIdN RVQhPh[ RbVeZ R\\H^I R[WWZ RbG`I RhIjNlO RVITNRN RZNWN RjHcI RSI[IZO RhNbO RVQUY',
	    'PoRFlF RXNZN RVShS R`I`N RdIcN RVPhPi[ R\\G^H R_C`F RhJkN R[UXW RdUbW RbG_J R^WfZ RVHUMRN RgMcN R_T]YX[ RSH[HZN RiHcH RVOVTUY',
	    'PoRFlF RUShS RR[l[ RVVV[ R\\V\\[ R`I`M RbVb[ RVVhVi[ RVPiOiU R_C`F RhJkM RaG_J RgLcL RVHULRM RcIcM RZMXM RSH[HZM RiHcI RVNUT',
	    'PoRElE RbLhL RVPiP RYVeV RYXeX RYRYX R_C_F R_I_L RYReReX RVNiMi[ RgIkL RVISL RbZf[ R\\ZW[ RcHcL RjGcH RSH[HZLXL RYT]T`T ReTaT^T RVMVSUY',
	    'PoRElE RbLhL RVQiQ RaSfS RYV]V RaVeV RXX]X RaXfX RWZgZ RXS]R]Z R_C_F R_I_L RaQaZ RVOiNi[ RhIkM RVJSL RdHdL RSH[HZLXL RjHcH RVMVSUY',
	    'PoRFlF R_C_F RePe[ RXWT[ RXF\\KaMjP RfFbK]MSP RYQXX',
	    'PoSFkF RaPfP RXS]S RaSfS RXW]W RaWfW RWZfZ RXP]O]Z RaNaZ RhOh[ R^C`F RXF[I_KhM R^KkN RfFdH_K[LRN RVOVTUY',
	    'Po_FiF RRJlJ RTRjR RTZjZ RTLT[ RWEWJ R_C_J RjLj[ RZM]P RZU]W R[TWX RdL_P RcT_X R[LWP RcNhP RcVhX',
	    'PoZF_F RRJaJ RSR_R RSY_Y Rd[k[ RSLS[ RUEUJ RZCZJ R_L_[ RdCd[ R[N]P R[U^W RWLUP RWSUX R\\LZP R[SZX RlWk[',
	    'PoYF_F RRJ`J RSR^R RSY^Y Rc[k[ RSLS[ RUEUJ RYCYJ R[U]W R^L^[ RcCc[ RlWk[ R[N]P RWLUP RVSUX R[LYP R[SYX RiLbO',
	    'PoYF_F RcHlH RRJ`J RSR^R RSY^Y Rb[l[ RSLS[ RUEUJ RYCYJ R^L^Z RlXl[ R[N]P R[U]W RbMjMgQdTbZ RWLUP RVSUW R[LYP R[SYW RdC`L',
	    'PobElE RYF^F RRJ_J R_OlO RSR]R RSY]Y RSLS[ RTETJ RYCYJ R]L]Z RbEbO RZU\\W RfR_Y RgOeT RVLUP RVSUW RZLYP RZSYW RiEi[e[',
	    'PoYF_F RRJ`J RbMlM RSR^R RSY^Y RSLS[ RUEUJ RYCYJ R^L^Z RhMh[ R[M]O R[U]W RWLUP RVSUW R[LYP R[SYW RkDbFbS`Z',
	    'PoYF^F RRJ_J RSR]R RSY]Y RSLS[ RTETJ RYCYJ R]L][ RhMh[ RhHlL RcH_L RfChI RVLUP RVSUW RZLYP RZSYW ReCbJ RbMbTaY',
	    'PoYF_F RRJaJ RSR^R RbShS RgUkU RSZ^Z Rb[k[ RSLSZ RUEUJ RYCYJ R_L_Z RbLb[ RbLhLhS RbHlHlU R[N]P RlXl[ RWLUP RWSUW R[LYP R[SYW RcD`K',
	    'PoYF_F RbIlI RRJ`J ReNkN RSQ^Q ReTkT RSY^Y RSLS[ RUEUI RYCYI R^L^Z ReHe[ R[M]O R[U]W RWLUP RWSUW R[LYP R[SYW RcCaL',
	    'PoYF]F RRJ^J R_KlK RSR]R ReUiU Ra[k[ RS[][ RSLS[ RUEUJ RYCYJ R]L][ RaDa[ ReCeU RiDiU RVLUQ RYLXP RVTUY RYTXY',
	    'PoYF]F RRJ^J RSR]R RSZ]Z Rg[l[ RSLSZ RUEUJ RYCYJ R]L][ R_J_[ RgDg[ RlIgN RlWl[ RVLUP RYLXP RVTUX RYTXX RcLeMbM RcDdY^Z',
	    'PoYF^F RRJ_J RdMkM R`MhM RSR^R RSY^Y RbZkZ RSLS[ RTETJ RYCYJ R^L^Z RbQb[ RbQkQk[ RZU\\W RiHlN RVLUP RVSUW R[LYP R[SYW ReCbM',
	    'PoYF^F RRJ_J RSR^R RaRkR RSY^Y RSLS[ RTETI RYCYI R^L^Z RaEaR RaEkEkR RiTl[ RcT`[ RVLUP RVSUW R[LYP R[SYW',
	    'PoYF^F RRJ_J RaLjL RSR]R RaSjS RSY]Y R_[l[ RSLS[ RTETJ RYCYJ R]L][ RaEa[ RaEjEj[ RVLUP RVSUW RZLYP RZSYW',
	    'PoYF^F RRJ_J RgNkN RSR^R RSY^Y RbZlZ RSLS[ RUEUJ RYCYJ R^L^[ RbQb[ RbQlQl[ R`ElElM R[N\\P RZU]W RdJ`N RWLUP RWSUW R[LYP R[SYW RfFcM',
	    'PoYF^F RRJ_J RbLiL RSR]R RSY]Y RSLS[ RTETI RYCYI R]L]Z RdPd[ RZU\\W RgGlL RcH_L RfChH RVLUP RVSUW RZLYP RZSYW RdCbI R_PjPjXfX',
	    'PoYF^F RRJ_J RbKiK RUQ]Q RgWjW RSY^Y RSMS[ RTETJ RYMYX RYCYJ R^M^[ RdOd[ RaOjOjW RXQ\\U RfClK RfC_K RVMVP RYPVU R[M[P',
	    'PoYF^F RRJ_J RSR^R RbThT RSY^Y Re[j[ RSLS[ RUEUJ RYCYJ R^L^Z RbMbV RbMhMhT R[U]W RWLUP RVSUW R[LYP R[SYW RdD`L RbHlHlUk[',
	    'PoTGjG RRJlJ R_MhM RROlO RUUiU RUZiZ RUQUZ RXLXO R_K_O RiQi[ RWDYF R[VXY RcP`T RcV`Y RfDdF R[PXS RbJjN RbWfX R[W^X R^C]GZKRN RbRfS RZR^R',
	    'PoYF]F RRJ^J R`LkL R`QkQ RSR]R RSZ]Z RSLSY RUEUJ RYCYJ R]L]Z R`G`S R`GkGkQ RZU[W RVKUP RVSUX RZKYP R[SYX RkSgW_[ RlWk[i[ RfCePgY',
	    'PoYF^F RaIkI RRJ_J RaNkN RSR]R RSY]Y RSKS[ RTETI RYCYI R]K]Z RaDaZ RaDkDkN RhWl[ RZM\\O RZU\\W RkQhT RfRiY RVLUP RVSUW RZLYP RZSYW ReNhX RgY_Z',
	    'PoSD]D RSG\\G RRJ]J RfJjJ R_LiL RROlO RTTjT RTZjZ RTPT[ RXCYKZO R`J`O RjPj[ RZW^X R[UXY RcQ_S RcW_Y R[PXS RcQgS RZQ^R RcWhY RdFbI^J R_DkEjJ',
	    'PoYF_F RRJ`J RbPfP RSR_R RbYlY RSY_Y RSLS[ RUEUI RYCYI R_L_Z RbGb[ RhGlGl[ R[U]W RfDbH RWLUP RWSUW R[LYP R[SZW RlPhP',
	    'PoYF^F R_GlG RRJ_J RSR]R RSY]Y RSKS[ RTETI RYCYI R]K]Z ReCeG RdTjZ RhJlN RfT`Z RbK_N RbOeW RhNeW RVLUP RVSUW RZLYP RZSYW',
	    'PoYF^F RRJ_J RaLjL RSR]R RfRkR RSY]Y Re[l[ RSLS[ RTETI RYCYI R]L]Z RaEaL RfLfZ RaEjEjL RZU\\W RVLUP RVSUW RZLYP RZSYW RbUbX RaP`Z',
	    'PoXF]F RRJ^J RbKjK RSQ]Q RSY]Y R`YlY RSKSZ RUEUI RXCXI R]K]Z R`D`[ RfGfW R`DlDl[ RfKjT RVLUP RZLXP RVSUW RZSYW RfKbU RfIiS RfIdR',
	    'Po`DkD RYF^F RRJ_J R_OlO RSR]R RSY]Y RaZjZ RSLS[ RTETJ RYCYJ R]L]Z RaRa[ RaIjIjO RaRjRj[ RZU\\W RVLUP RVSUW RZLYP RZSYW RdEbO',
	    'PoXF]F RRJ^J RSQ]Q RSY]Y RSLSZ RUEUI RXCXI R]L]Z RfLkLj[ RVLUP RYLXP RVSUW RZSXW R`JdJdQ`R RdD`F`T_Z RkDfFfReY',
	    'PoXF]F R`FkF RRJ^J RSR]R R`WfW RSY]Y RSLS[ RTETI RXCXI R]L]Z R`Q`X R`QfQfW ReHhIjL ReHaL RVLTP RYLXP RVSUX RZSXW ReCdJ R_NkNj[f[',
	    'PoYF^F RRJ_J RgKkK R`QkQ RSR]R RSY]Y Rg[l[ RSLS[ RTETJ RYCYJ R]L]Z R`E`Q RhQh[ RgEkEkQ RlXl[ RbW_[ RVLUP RVSUW RZLYP RZSYW RdD`E RdQaY RdK`KdK',
	    'PoYF^F R`GlG RRJ_J R_LlL RSR]R RbUkU RSY]Y RbZkZ RSLS[ RTETJ RYCYJ R^L^Z RbPb[ RcCcL RiCiL RbPkPk[ RZU\\W RVLUP RVSUW RZLYP RZSYW',
	    'Po^DlD RXF\\F RRJ^J R`QdQ RSQ]Q RSY]Y RSLSZ RUEUI RXCXI R]L]Z R`D`W RdDd[ RhSk[ RjRf[ RVLUP RYLXP RVSUW RYSXW RgOjY RkNgY RdW^W R`JdJkJhX RfIhW RdJ_K',
	    'PoXF]F RRJ_J RaMlM RSR]R RbWjW RSY]Y Rh[l[ RSLS[ RTETJ RXCXJ R]L]Z R`Q`[ RaHaM RfDfV RaHlHlM R`QlQl[ RiTjW RVLUP RYLYP RVSUX RYSYW RkDaE',
	    'PoXF]F R`IkI RRJ^J R`PkP RSR]R R`SkS R_WlW RSY]Y RSLS[ RTETI RXCXI R]L]Z R_E_H R`M`S ReGe[ R`MkMkS R_DlElH RVLTP RYLXP RVSTW RYSXW',
	    'PoXF]F R`FkF RRJ^J R`LlL RSR]R RSZ]Z RSLSZ RUEUJ RXCXJ R]L]Z RbHcK ReDeF RhHfL RVKUP RYKXP RVSUX RYSXX RhObQ RjRbU RkWb[ R`L`T^[',
	    'PoXF]F R`IkI RRJ_J R`MkM RSR]R RaWjW RSY]Y Ri[l[ RSLS[ RTETI RXCXI R]L]Z R`Q`[ R`E`M RfEfV R`EkEkM R`QlQl[ RhTjX RVLUP RYLYP RVSUX RZSXW',
	    'PoXF]F RRJ^J R`JdJ RgJkJ R`MkM R_QlQ RSR]R RSY]Y Rd[i[ RSLS[ RTETJ RXCXJ R]L]Z R`D`J R`DdDdJ RgDgJ RgDkDkJ RVLTP RYLXP RVSUX RYSXW RcQaUjUi[',
	    'PoXF]F R`IkI RRJ^J R`LlL RaRkR RSR]R RbUkU RSY]Y Ra[l[ RSLS[ RTETJ RXCXJ R]L]Z RfRfZ R`DkDkI RhOlR RVLTP RYLXP RVSUX RZSXW ReLcQ R`D`S_Z',
	    'PoXF]F R`JkJ RRJ^J RSR]R RSZ]Z R_[l[ RSLSZ RTETJ RXCXJ R]L][ R`R`[ RdRd[ RgRg[ R`RjRk[ RgLlQ RdM_Q RaDcH RVLUP RYLXP RVTUX RYTXX RiDhH',
	    'PoTFjF RUHiH RRJlJ RYLiL RWNkN R^PiP RRSlS RU[i[ RUTUZ RWPWS R^P^S RaLaN RUViUi[ RZC\\E R[TXU R[XXY RcS`V RcX`Y RdCbE RcTgU RcXgY RZT^U RZX^Y R_FZJSN',
	    'PoXF]F RRJ^J RaKiK RaRlR RSR]R RaWiW RSY]Y Rg[k[ RSKS[ RTETI RXCXI R]K]Z RaTaW RhHhK RhThX RbP^T RVLUP RZLXP RVSUX RYSYW RdM_O RkNhN RdY_[ RbE`HaL RaFlFkO RlSk[ RdGdM RdSdY',
	    'PoYF]F R_FkF RRJ^J RaMjM RaPjP RSR]R RaSjS R_VlV RSZ]Z RSLSZ RUEUJ RYCYJ R]L]Z RaIaV RaIjIjV RhXl[ RbX_[ RVKUP RYKYP RVSUX RYSXX ReCdI',
	    'PoXF\\F RfFlF RRJ]J RSR\\R ReUkU RSZ\\Z Rd[l[ RSLSZ RTETJ RXCXJ R\\L\\Z RbCbN RfPf[ RhChN R^PkPkS RaUcZ R^FbFdJ RhEkL RUKTO RYKXO RUSTW RYSXW RbE_L RhDeL RaS_Z',
	    'PoXF\\F RfFlF RRJ^J R`OjO RSQ]Q R^SlS RSY]Y Rb[e[ RSLSZ RUEUJ RXCXJ R]L]Z RbCbN ReSe[ RhChN RhVlY R_FcFdJ RaV^Y RhEjK RVLUP RZLXP RVSUW RYSXW RbF_L RhEeL',
	    'PoZD`D RTFaF RVG^G RbGlG RUI`I RXO^O R_QiQ RRSlS RU[i[ RUUU[ RTFUMVP R_P_S RVM`M`P RUWiVi[ RhKlO RhDjF R[SXV R[WXZ RcS`W RcWaY RfFiN RcUgU RcXfY RZU^U RZX^Y RcOaO RWLZL R_L[L RTHSP RfCeM',
	    'Po_GiG RRLlL RTZjZ RTNTZ RWEWL R_C_L RjNj[ RcTfW R^QbS R^QXW R_N\\T',
	    'PoYG^G RRL_L RSZ^Z Re[l[ RSOSZ RUEUL RYCYL R^O^[ ReDe[ RlXk[ RXR[U RkHbQ RYNWV',
	    'PoXG^G RbHlH RRL^L RSZ]Z Rb[l[ RSNSZ RTETL RXCXL R]N][ RXR[U RdSaZ RbMjMfPeS RlWl[ RdD`K RXNVV',
	    'PoXG^G RRL^L RaLlL RSZ]Z RSNSZ RTETL RXCXL R]N]Z RhLh[ RXRZT[V RXNVV RkDaFaR_Z',
	    'PoYG^G RbLjL RRL_L RbSjS RSZ^Z R`[l[ RSOSZ RTETL RYCYL R^O^[ RbEb[ RbEjEj[ RXR[U RYNVW',
	    'PoYG]G RRL^L RSZ]Z RSNSZ RTETL RYCYL R]N][ RbVg[ RdKfN RgHlM RcI_M RXQZV RfDhJ RaPjPgW ReCbJ RXNVW',
	    'PoYG]G RRL^L RaShS RgUkU RSZ]Z Rb[k[ RSNSZ RTETL RYCYL R]N][ RbLb[ RaLhLhS RbHlGlT RXQZV RlXk[ RcD`J RXNVW',
	    'PoZG_G RRL_L RgNkN RSZ^Z RbZkZ RSNSY RUEUL RZCZL R^N^[ RbQb[ RbQkQl[ RXR\\V RdJ`N RYNWW ReEcL RaElEkN',
	    'PoXG\\G RRL]L RSZ\\Z Rg[l[ RSNSZ RTETL RXCXL R\\N\\[ R`J`[ RgDg[ RlWl[ RkJgN RWQZW RcDdY^Z RXOUX RcLeMbM',
	    'PoXG^G R`JjJ RRL^L R`PjP RSZ]Z RSNSZ RTFTL RXCXL R]N][ R`E`[ R`EjEjP ReUk[ RiTfU RYS[W RcPgX RfZ_[ RXNVW',
	    'Po_DkD RYG^G RRL^L R_OlO RSZ]Z RaZjZ RSNSY RTFTL RYCYL R]N][ RaRa[ R`IiIiO RaRjRj[ RYS[W RXOVX RdDbO',
	    'PoYG^G RRL_L RaLkL RfSkS RSZ]Z RSNSY RUEUL RYCYL R]N][ RaDaL RaDkDkL RXR\\V RaTdY RYNVW RaQ`[ RfLeZk[',
	    'PoXG^G RRL^L RaMlM RbWjW RSZ]Z Rh[l[ RSNSZ RTETL RXCXL R]N]Z RaQa[ RaHaM RfDfV RaHlHlM RaQlQl[ RXRZU RiTjX RXNVV RkD`E',
	    'PoYG]G R`IkI RRL^L R`LlL RbRkR RbVkV RSZ]Z Ra[l[ RSNSZ RTFTL RYCYL R]N][ RgRg[ R`DkDkI RYS[W RhOlR ReLcR RXOVX R`D`T^[',
	    'PoaFlF RRF^F RRL_L RaNjN RaQjQ RTR]R RaTjT RTV]V RaWjW RY[][ RTOT[ RXCXF RTO]O][ RaCaJjJjN RbNb[lZl[ RUGUK R\\G[J',
	    'PoTElE RcIkI RVMaM RcPjP RcRjR RXS_S RcUjU RXW_W RcXkX Rc[l[ R\\[_[ RXPX[ RYJYM R\\G[I RXP`P`[ RcPc[ RcGcLjLjP RWI_I^M RTETRS[',
	    'PoaElE RSE^E RRH_H RaIjI RTM]M RaOjO RTO]O R[Q]Q RRUlU RTJTQ RTJ]J]Q ReRe[ RaCaGjGjI RVFUI R[FZH RbIbQlPlQ RYWWYS[ RaMdMaM RaKhLjM RZRYX RhMfM RdMgM',
	    'PoUFlF RcJkJ RWNaN RcPjP RcRjR RXT`T RcUjU RXW`W RcXkX Rc[l[ R\\[`[ RXQX[ RYKYN R\\G\\J R`C`F RXQ`Q`[ RcPc[ RcGcLjLjP RWJ_J^N RUFURS[',
	    'PoaElE RRH_H RaIjI RTL]L RaOjO RTN]N RaQlQ RRSlS RSVkV RTJTQ RUEUH RbIbQ RaGjGjI R_UbY RSE]E[H RaCaG RaKeKaLdMaM RTJ]J]QZQ RgKjK RdKgK RgMjM RdMgM RcYl[ RiKfK RhMfM R_S^W[YR[',
	    'PoaElE RRE^E RRH_H RaIjI RTL]L RaOjO RTN]N RaQlQ RZQ]Q RUSiS RRVlV R[[_[ RTJTQ RTJ]J]Q R_V_[ RbIbQ RaGjGjI RUEUH ReWlZ R[E[H RYXR[ RaCaG RaMdMaM RgKjK RgMjM RdMgM RaKeKaK RiKfK RhMfM ReKhK',
	    'Po\\FcF ReFlF RRGZG R[LcL ReNkN RTPYP ReQkQ R]RbR ReTkT RTUYU R]VbV ReWkW RW[Y[ RUJU[ RTLZLZ[ R]O][ ReCeIkIkN RfNf[lZl[ R]G^J R_C_F RVGRO RbH`L RVCTO R]ObOb[`[',
	    'PoaElE RSE^E RRH_H RaIjI RaKjK RTO]O RaOjO RTTjT RRWlW RTJTQ RZRZW RdRdW RaCaGjGjI RUEUH RbIaQlPlQ R[E[H RcYl[ RTLWLTL RaMdMaM RTJ]J]QZQ RZYR[ RgMjM RZL]LZL RhMfMdMgM',
	    'PoWKgK RaNkN RSN^N RRR^R RaSjS RTV\\V RaWjW RTX\\X RaYjY RY[\\[ RTTT[ RWIXLXN RTT\\T\\[ RWIgIgK RaKaPjPjS RbSb[lZk[ R[N[R RUOUR RZGcFlH R^DcD ReUaU R^CYFRH RhUjU RiUfUdUgU',
	    'PoZF^F RgFlF RRFXF R_FeF RRLXL R_LeL RZQ^Q RgQkQ RSRWR RZT]T RgTkT RZW^W RgWkW RZ[^[ Rg[l[ RSNS[ RTHTK RUCUG RSNWNW[ RWHWL RZNZ[ R`N`[ RaHaK RbCbG RhNh[ RdHdL RZCZI]I]MZN RgCgIkJjNgN RWUSV RdR`R RdU`U R`NdNd[b[',
	    'PoUDiD RTKWJ\\JbJfJjK RaOkO RSO^O RRR_R RTX]X RaYjY Ra[l[ RZ[][ RSFSI RTTT[ RTT]T][ R_D_J RbSb[ RaKaM RSFkFkI R[O[R RdMhM R\\M`M RTKTMXM RYKYMVM RVOVR RTVWVTV RdSaS RZV]VZV RaOaQjQjSgS ReHhH RdSgS RaWcVhVjW RXHVH RhWeW R[HXH[H RdWgW RcHfHcH',
	    'PoaDkD RSE^E RRG_G RaMeNkO RTM\\N]O RRTXT R_TeT R`YeY RY[^[ Rg[l[ Rb[e[ RSUS[ RTITO RZTZ[ RTI]I]N R`U`[ RaHaN ReUe[ RhTh[ R`QeQcT R[E[H RRQWQVT RSQTT RaQbT RVEVG RZK]K R`WdW RVUSVWVW[U[ ReWaW RgPgRjRkR RgXiWjV RYX[W]V RaCaFjGfH RUKWKTK RdHfHdHaIhJiK RfKcK RYPYR\\R^SYT RWYRY',
	    'PoRJlJ Ra[l[ RaJaZ RcEfFhH RYUS[ RlXl[ R[PWW RhN`WZY R\\D\\IZT',
	    'PoRGlG RaNlN RTRjR RRVlV RZOZV RaGaM RdOdV R\\CZIWLRN RcXl[ RbDhE R[XR[ RiIbKZM',
	    'PoXHeH RVNhN RRSkS Ra[k[ RVKVN RaSaZ RVKhKhN RkXk[ RcPgR R]P[VXYR[ R`ElH RhV[Z R`DZGRI',
	    'PoVL]L RaLiL RURZR RSWaXjX RUZZZ R^[k[ RUUZUZZ RUNZNZR R^H^[ RaHaZ RUHiHiL RdQhU RgRcV RSPaOjOkXk[ RYEbE_H RZCUHRI RVGVL',
	    'Po]KbK ReKkK RSK[K R\\R`R R[VeWkX R\\Y`Y Rb[l[ RWEW[ R]G]K R\\M`M`R R\\T`T`X RcHc[ ReHe[ R\\HkHkK R[PeOkNkX RVMZQ RgQjT RaC[I R_EfEcH RiPgV RWKRU RWIUQ RZDRE',
	    'PobGlG RXNaN RUPeQiQ RSYjY RT[Y[ R][l[ RSGSL RYCYK RTSYSYV RYXY[ RVQ]P][ RSG^G^L RaOa[ RSTjTjY R[H\\J RVDRF RdD`H RbFeJlL R\\D_E RVHUK RYMSP RiFgI`L RcVgVbX RTVWWYX RWWTW',
	    'PoUPiP RUUiU R_[k[ RVIVW R_J_[ RTKiJiU RlWk[ R[CRL RYFdE_J',
	    'PoYIeI RTPZP R\\PbP RdPjP RUWiW Re[i[ RTLTP RURU[ R\\R\\[ R\\LbLbP RbRb[ RdLdP RURiRi[ RdLjLjP R\\L\\P R_CbFlJ R^C[GRK RTLYLYP',
	    'PoVI]I RSPVP R]P`P RTV_V RSLSP RTRT[ RSLVLVP RXRX[ RXL[L[P R[R[[ R]L`L`P RZC_I RdWa[ RYCSJ RhSkZ RhHgRcZ RgOiY RcIkHjN R]L]P R`L]L R[LXLXP[PXP RdCbN RgMhW RgLeV RTR_R_[][',
	    'PoVI]I RaLkL RTV_V RTRT[ RVKVO RWRW[ R[R[[ RfEf[ RZC_H RYCSI RfKkV RfK`W RXLXO R]L]O RfIcS R[KXK RVKSKUOSO R[K[OXO R_K]K_K^O_O\\O RTR_R_[][ RjD`F',
	    'PoVI]I RbJiJ RaMlM R\\O_O RSV_V R][_[ Rc[f[ RSRS[ RSLVLVO RWRW[ R[R[[ RSR_R_[ RfMf[ RhUlY RYC_I RjQgS RYCSJ RdUaY RbPcS RfOiW RfMhV RXLZLZP R\\L\\P R\\L_L_P RcCbK RVLSLSOVOSO RZLXLXOZOXO RcFiFhM',
	    'PoaGeG RUI\\I RbUkU RSV^V RbZkZ R\\[^[ RSRS[ RWRWZ RZKZO R[R[Z RSR^R^[ RaCaM RbQb[ RbQkQk[ RhChMlLlM RfNeQ RYD_G RlFgH RYCUGRH RSKSO R\\K\\O RUKSK R_K\\K RUKUOSO RZKXKYKXOZOXO ReL`M R_K_O\\O',
	    'PoaEkE RVI]I RbMkM RSPVP RbQkQ RbVkV RTV_V RSLSP RTRT[ RWRW[ RXL[L[P R[R[[ R]L`L`P RbIbU RgEeI RbIkIkU RiXl[ RZC_I ReXa[ RYCSJ R[LXL RSLVLWNXP[PXP R_L\\L]P`P\\P RTR_R_[][',
	    'Po_PjP RhZkZ R\\H\\K R^S^Y R_K_P RdPd[ ReCeH R_KjKjP R^SkSkY R\\HlHlK RVCVS[W R_D`G RjDiG RZHYM RVSR[ RSHSO RVMUX',
	    'Po\\GiG R\\JiJ RRMXM R\\MgM R[QlQ RRUXU R^WiW R^ZiZ RRERX RREXEXU R\\D\\J R_S_[ RbKbQ R\\DiDiJ R\\TiTi[ RkKhN[U',
	    'PoREkE RXKhK RXNgN RXRgR RXViV RRZlZ RRER[ RYJYW R`J`V R_G`I RXJTN RYGWM',
	    'PoRF]F RaFlF RRK_K RUN\\N RaPlP RST^T RRW_W RSQST RUIUN RYNY[ RYCYH R\\I\\N RSQ^Q^T ReCeH RhPh[ RkIaKaT`Z',
	    'PoTG^G RSM_M RYR_R R[[l[ RVVYY RdJfO RaT]X RUSWX RhOkW RcM_W ReEbS RZCYYaZ RUPSZ',
	    'PoRG^G RaLiL RSN]N R_RlR RSR]R RRV^V RSJSR RXCX[ RSJ]J]R ReLe[ ReElL RdG_L ReCcI',
	    'PoTJ[J RaKjK RSO\\O R`PjP RcWjW Rh[l[ RXJXY R^E^[ RcPcW RdMfP R^ElEl[ RYE\\H RXDRJ RbGcJ RiGhJ R[QZV R\\XRY RTRUW',
	    'PoSD\\D R_HkH RSI[I R_LkL RSM[M R^QkQ RSQ\\Q R]UlU RW[Z[ RTDTQ RXDXQ R_D_L RbNbU ReDeL RhNhU R_DkDkL RhWk[ RbW^[ RYSYV RWTXW RUTUX RRTRY R[SZ[',
	    'PoSD\\D RSI[I RSM[M R^OjO R^RjR R^TkT R]WlW RW[[[ RUTUX RWTWW RXDXQ RSDSQ\\Q\\Z R^L^Q RaRaW RdLdQ RgRgW R^LjLjQ R^F`G RfFgG RYSZV RfYk[ RcH]I RkGeJ RbY][ R]DcDcK`K ReDkDjKgK RRTRY',
	    'Po_IlI RTJ[J RSO\\O RXJXY RXD\\H RiDkF RgUa[ RXDRJ RjMfW R[RZV RTRUW R\\XRY RfCfLhY R_I_S]Y RlXk[i[',
	    'Po_RiR R_[l[ R_M_Z RTZR[ RXPUX RWSYV\\Ye[',
	    'PoRZlZ RRER[ RRElEl[ R`PfU R[L_P RYJ\\M ReHdL[TVV',
	    'Po]FlF R`JkJ RTJZJ RRO[O R`PjP R`TjT R_WlW RWJWY R`M`T ReGe[ R`MjMkT RWD[H RdCeF RWERI RZRYV RTRTW R[XRY R]F]U\\[',
	    'PoSG]G RaGkG RRK^K R`KlK R[P^P RhQlQ RSU^U R`UkU R_[l[ RXCXK R[K[P ReCeK ReQe[ RhKhQ RVOSR RVLUP RcLbQ_Q RXQZZR[ R]ZU[',
	    'PocT_V RRKlKbT RWRdY',
	    'PoTLjL RRRlR RXUR[ R\\JWW RTG]FYS RhD[F',
	    'PoTKkK RURjR RRYlY R_E_Z',
	    'PoSMkM RRYlY RYEYZ RdEdZ',
	    'PoRORY R_F_P RROlOlY RVHZP RiHeP',
	    'PoULjL RSRkR R[TTZ RWFYL RbRhYlZ R]MYW RfFcL R^E\\S',
	    'PoRGcG RTNaN RTRaR RRVcV RTKTR RZCZJ RZRZ[ RTKaKaR',
	    'Po[IcI R\\MbM R\\PbP RSRZR RdRlR R\\ZbZ RVUWZ R\\T\\[ R\\TbTb[ RhUhZ RhNdR RWNSR RYOZS RYUZZ R_E_H RkUkZ RjOlS RjIgO RWDSM RYIVO RhDdM RSUR[ ReUd[ RXLSL RiLdL',
	    'PocEjE RRJYJ R_JhJ R^UiU R^XiX R^ZiZ RVCVU R\\H\\K R_Q_[ RcCcH R]SiRi[ R\\HkHkK RhLjO R_L\\O RdMhRlS RYSRV RcM^R[S',
	    'Po]OjO R]TjT R^H^T RbWb[ RdIdT R\\JjJjT RWCWS[W ReVf[ RjWk[ R[IYM RVSS[ R`C]J R`FfFdK R]V[[ RTIRO RWNUW',
	    'Po_QiQ Rf[j[ RYCYN RTH]H]L RdJdZ RVQ[V\\Y RcQhV RZUU[ RbS^W R_KaO RTN\\OXX RbC_K RdOaU RhKgO RaHlHlTj[ RTHTTR[',
	    'PoSFlF R[LjL RYQkQ R[UjU RYZlZ RWMW[ RZCZH RbJbZ RdCdH RVNRR RXJUP',
	    'PoRElE RTH_H RWJ]J RhKkK RaLjL RTP_P RRRlR RbVjV RTXjX RT[j[ RTHTP RTTT[ RZCZG RbRbV RdCdG RbHhHhJ RTTjTj[ RbHaK RUL[L^N RZJYMVO RcNkQ RhN`P R\\R\\VWW',
	    'PoSDZD RRH[H R]JkJ RSKZK RSOZO R\\RlR RSZZZ RSSS[ RSSZSZZ R`D`R RhDhR RgUlZ RaV\\[',
	    'PoRG\\G R_IiI R_MiM RSN\\N R_OlO RSR\\R RRV\\V Rf[j[ RSJSR RWCW[ RSJ\\J\\R RaVaY R_FiFiL RfUiW RdCcF RdUeX R^V]Z R_F_RkSj[',
	    'Po[HkH RRKXK RRQXQ RRVXV RRERY RREXEXV ReTjZ R\\J`R RcCcPfW RgJkR RhLfR R]LZR RaT]YY[ RcKbT',
	    'PodOiO RRSlS R_P_[ RYJTP RUDXE R]HeL R`SdWlZ RbF`K]NZO R]SZWRZ RSGWH R\\EkEiO',
	    'Po]DjD R^JaJ RfJiJ R]MaM RfMjM RWMW[ RdDdL R\\G\\HkGlJ RWNZQ RUDWF RcQeR R]TiTdY RWMRR RcNlR RaXdY RRHZHVO RcN[R',
	    'Po]HlH RXCXH RXNX[ RXN\\R RZLRS RaCaNdUhYl[ RSI\\IYM R`R\\Z RaNjNgUcY_[ RaI_V',
	    'Po^FgF RYOlO R[TjT R^[b[ RWIW[ RbJb[ R\\JjJjU RfGbK RVJRN R^FYK RYCUL R_C]H',
	    'Po^NjN RXLX[ R^EjEjN RXCTH RZIRQ R_NaSgXlZ R^E]SZ[ R^N\\X',
	    'Po[FkF RYZlZ RWMW[ R[NiNiZ RWDRI RWKRQ RXIWM RaF^Z',
	    'PoRF^F R`FlF RdQjQ RYZlZ RYCYH R^N^Z RdKdZ ReCeH RWVS[ RTJYL RROXQ',
	    'Po\\FlF R]IjI R^OiO R\\SlS R^[j[ RWNW[ R^V^[ RdCdI R^KiKiO R^VjVj[ RWOZR RVCWF RXMRS R[NYP R`PaR RgPgR RRIZHWP R^K^O',
	    'PoYGlG R\\NiN R\\RiR R_[b[ R\\K\\R RbCcG RbRb[ R\\KiKiR RgUlY R^UYZ RTDWF RRKWM RWRS[',
	    'PoRJZJ R]QkQ R]WlW RVDVU R]S]W RdNd[ RbC\\I R`GdKlN R`FjFfJ\\N RZSRV',
	    'PoSE[E RaJkJ RUX[X Rc[l[ RULUZ RTN[N[X R]M`M`X\\[ R]F_I RfCfMjU R_Ye[ RWHRQ RWEUO RfLcV R_M\\M RfIeT',
	    'Po]FlF RTG[G R^JkJ RTK[K R]NlN RSQ[Q RWKWZ RdCdM RbO\\U RaSfXl[ RUCRJ R[WVZ RaRjReW][',
	    'Po`ElE RTO[O RhQkQ RTT[T RcTiT RaYkY RTITT RXJXT RXVXZ RSK[J[T RaJa[ RdEdO RhEhQ RaJkJk[ R\\K_N R]D^H RZV[X RVDRK RUFYFWK RSVS[ R^R\\[',
	    'PoXEjE RZPiP RW[l[ RaEa[ RSEWI RgVjX RfSiW RWRR[',
	    'PoRElE RRIlI RVMVV RZCZG RdCdG RVMhMhV R`I\\M RaWlZ R_T\\WXYR[ R_O^U',
	    'PoRElE RSIlI RVPhP RVShS RVWhW RVMVW RZCZG RdCdG RVMhMhW R_I\\L RcYl[ R[YR[',
	    'PoUG\\G RTL\\L R`MfM R]PlP RSR\\R R_XfX Re[i[ RXLXZ R_S_Y R`H`M R`HfHfM R_SfSfX R]EkEi[ RVCSK R\\XVZ',
	    'Po^OlO RTP\\P RTU\\U RTITU RXJXU RSK\\K\\U RdHfL RWCTI RVF[FYJ RaDjDj[f[ RaDaR_X R]XRY',
	    'PoZLeL RXWfW RS[k[ RXPXW RXPfPfW R_DdJlM R_DXLRN',
	    'PoYPkP R[TgT RS[k[ RWNWY R[KhKhV RYKRR R`FkK RaKaT]WZX R_C[GRK',
	    'PoWK`K RcMiM RXR_R RbYjY RVGV[ RXE_E_T RbQb[ RbQjQj[ RiJlN RfDjL RfCaN RVCSM R[E[RYX',
	    'PoaGlG RWK`K RbQjQ RXR_R Rg[l[ RVGV[ RXE_E_T RbKbQ RfCfK RhQhZ RbKjKjQ RlWl[ RcW_[ RUIRO RVCTM RdQbY R[E[RYX',
	    'Po_IdI RVK^K RWR\\R ReZkZ RUGU[ RWE]E]S RaCa[ ReRe[ ReRkRk[ RhMlR RhMcR ReGiP RfGjGfP RVCSN RfDdK RaH^V RZEYRXX',
	    'Po_DlD RWF]F RVJ^J Rh[k[ RZCZJ R`J`[ ReDe[ R`JkJk[ R[L^O RaTdW RWT]Y RaNdP RgNiQ RiFlJ R[UVZ RXMUO RbF_J RSDTI RdSaY RiLgR RiSgY RYOVV RXR]QYX RcLaR RSQRZ',
	    'Po_DlD RRI^I RS[U[ Rh[k[ RUCU[ RYNYW RZDZG R`I`[ ReDe[ RgNiP R_JkJk[ RaTdW RgUjX RaNdP RiFlJ RbF^J RdSaY RiLgR RiSgY RcLaR RWORR RXLYQ R^YWZ R\\L\\Q[Z',
	    'Po_DlD RRIWI RXK^K RWN^N RUCU[ RXDXK R`I`[ RfDf[ RgUjX RaTdW RaNdP RiFlJ RdSbY RiLgR RjSgY RdLaR R]R]ZZ[ RZGZK RZR^R RbG_JkJk[h[ RVLWQ RZDZH R^D^H RUGSU R[LZNZSXY RXD]D]I_K[K',
	    'Po_DlD RRDYD RZGZU R]C][ R`I`[ ReDe[ RaTdW RgUjX RaNdP RgNjP RiFlJ RdSaY RiLgR RiSgY RTPWSS[ RdLaR R[[][[[ RbF_JkJk[h[ RUDSQ RTJXJVV',
	    'PoRKYK R\\[_[ Rc[f[ RWEW[ R[F[U R_D_[ RfHf[ RVKYS RcHlHjL RdDaL RiNlV RVKSV RcNaV RYDRF RVHUR',
	    'Po_DlD RRKYK RWEW[ RZFZT R`I`[ ReDe[ RaTdW RgUjW RaNdP RgNjP RiFlJ RUKYS RdSaY RiLgR RiSgY RdLaR RVKRW R]C][[[ RbF_JkJk[h[ RYDRF RVHTS',
	    'Po_DlD RYN_N RYR_R RYV_V RYZ_Z Rh[k[ RSVR[ RUVU[ RWDWI RWVWZ RZLZ[ R[D[I R]K]Z RSD^D_I`[ RaNdP RfDf[ RgUjW RgNjP RSDSI_IkJk[ RaTdW RiFlJ RbF_J RdSaY RiLgR RiSgY RUJSO RWLST RdLaR RVNRO RZJWQWTRT',
	    'PobLiL RaZiZ RTETW RREXEX[ R[E[[ RaQa[ RaQjQj[ RgGlL RcI_M ReChI ReCaK R]L_T RTJXK RTPXQ R[E_E]M RYURW R^W\\V',
	    'Po`GlG RTKXK RTQXQ RaQkQ Rg[l[ RTETW RREYEX[ R[E[[ RaKaQ RfCfK RhQh[ RaKkKkQ RlXl[ RcW`[ R]L_S RYURW R_V\\V R[E_E]M RdQcY',
	    'PoeZjZ RTETW RREXEX[ RZEZ[ RaCa[ ReQe[ RdRjRj[ RhMlQ RhMcR R_IbIcO RfHiO RjGfP RfFjF RaI^T RfCdK RYURV R]V[V RZE^E\\M^Q^W RTKXKTK RTPXPTQ',
	    'Po_DlD RSKXK RSQXQ Rh[k[ RSFSY RSFXFXP RZEZ[ R`I`[ ReDe[ RgUjX R_KkJk[ RaTdW RaNdP RgNiQ RiFlJ RbF^K RTCUF RdSaY RiLgR RiSgY RcLaR RWWRY R^Q^X[X RWTXZ RZE^E\\M^V',
	    'PoSG\\G RgJlJ R`LeL R`OeO RST\\T RTMTS RUCUJ RTMZMZS R^K^[ R`F`L RcCcL RaQdQdX ReFeL RXW\\[ RiVd[ RhGiRfY R_C\\J RYCXJWTTZ R_I\\P RWPXX RhCgMiX RjJiV RaQ`Z',
	    'Po_DlD RXN\\N RWS]S RWW]W RVZ^Z Rh[k[ RRDR[ RZPZZ R`I`[ ReDe[ R^JkJk[ RYI^N RaTdW RgUjX RaNdP RgNiP RiFlJ R\\HVN RbF^J RXF[K RdSaY RiLgR RiSfY RYCVI RXF]FZK RWPWU RcLaR RRDUDTKUQUTST',
	    'PoSD\\D R_DlD RYK\\K RSNVN RSV\\V RSZ\\Z Rh[k[ RRHRL RSQS[ RXDXO RXQXZ RSQ\\Q\\[ RRH]H]L R`J`[ ReDe[ R`JkJk[ RaTdW RgUjX RaNdP RgNiP RiFlJ RbF^J RdSaY RiLgR RiSgY RcLaR RTKVKTK RZN\\NYN',
	    'Po_DlD RTK]K RTN]N RTW]W RTZ]Z Rh[k[ RTST[ RUGUN RWPWR RYGYN RSH]H]N RTS]S][ R`J`[ RfDf[ RgUiW R`JkJk[ RaTdW R]O^R RaNdP RgNjQ RiFlI RVCUFRI RVE[EYI RTPSR RbF_I RZPZR RdSaY RiLgR RiSgY RdLaR',
	    'PoaDgD R]HhH R[VlV R[X[[ RhWl[ RSGVI RTDXE RVXS[ R]DZG RRMVMVRRU RbWc[ RbKcOiP RhElG RcHZL RbLZO RbOZR RiJcLcR RcS_S RYTaU',
	    'PoVFjF RWHgH RWRbR RSWeW RUMUU R\\N\\T RUMdMdU R^SbT RXNZP R`N_P RXDRI R\\KZM R^Ye[ R\\QZTVU RZYS[ RlXk[i[ RTKhKhThZ',
	    'PoSE\\E R_OlO RUX[X R_ZlZ RUKUZ RTN[N[X R_E_[ RfEfY R_ElEl[ RWETP',
	    'PoTG[G RTL[L RRQ\\Q Ra[d[ RWLWZ ReGe[ R`GlGjK RVCSI R`N]V R[XVZ RiMkV R`C^L',
	    'PoUFlF RWIkI R[MfM ReYhY RWPWS RZSZY R[G[M R`C`F RaGaM RaPa[ RgGgM RZShShY RWPjPjS RUFURSZ',
	    'PoTG\\G RfOiO RTUjU RXCXO R_Q_[ RYR[T ReRcT R`MlS RaViZ RTDSI R`C^I RcE`K RgEdM R]NRS R]WT[ R\\JRK R`FkFiO',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'Pf[X\\Y[ZZY[X[Z R[RZF[E\\F[R[E',
	    'PfYDYG R]D]G',
	    'PfULdL R[CU^ RbUSU R\\^bC',
	    'PfUYXZ]Z_Y`XaVaT`R_Q]PYOWNVMUKUIVGWFYE^EaF R[D[[',
	    'PfS[cF RVFXGYIXKVLTKSITGVF RbZcXbV`U^V]X^Z`[bZ',
	    'Pfc[b[aZ^WYQWNVKVIWGYFZF\\G]I]J\\L[MUQTRSTSWTYUZW[Z[\\Z]Y`UaRaP',
	    'Pf\\EZI',
	    'Pf`C_F^J^T_X`Z',
	    'PfUCWFXJXTWXUZ',
	    'Pf[I[P RUM[PaM RWV[P_V',
	    'Pf[I[U RUOaO',
	    'PfUWVXUYTXUWUY RVXV[U\\',
	    'PfUOaO',
	    'PfUWVXUYTXUWUY',
	    'PfbETY',
	    'PfZF\\F^G_H`JaNaS`W_Y^Z\\[Z[XZWYVWUSUNVJWHXGZF',
	    'Pfa[U[ R[[[FYIWKUL',
	    'PfUHVGXF]F_G`HaJaL`OT[a[',
	    'PfTFaFZN]N_O`PaRaW`Y_Z][W[UZTY',
	    'Pf^M^[ RYETTaT',
	    'Pf`FVFUPVOXN]N_O`PaRaW`Y_Z][X[VZUY',
	    'Pf_F[FYGXHVKUOUWVYWZY[][_Z`YaWaR`P_O]NYNWOVPUR',
	    'PfTFbFY[',
	    'PfXOVNUMTKTJUHVGXF\\F^G_H`J`K_M^N\\OXOVPUQTSTWUYVZX[\\[^Z_Y`W`S_Q^P\\O',
	    'PfVZZZ\\Y]X_U`Q`I_G^F\\EXEVFUGTITNUPVQXR\\R^Q_P`N',
	    'Pf[T\\U[VZU[T[V R[I\\J[KZJ[I[K',
	    'Pf\\T\\U[WZX R[H\\I[JZI[H[J',
	    'PfbETObZ',
	    'PfTKbK RTTbT',
	    'PfTEbOTZ',
	    'Pf[X\\Y[ZZY[X[Z RWFYE^E`FaHaJ`L_M]N\\O[Q[R',
	    'Pf_L^K\\JZJXKWLVNVPWRXSZT\\T^S_R R_J_R`SaSbRcPcKbI_G[FWGTISKROSSTVWX[Y_XbV',
	    'PfVU`U RT[[Fb[',
	    'Pf\\P_Q`RaTaW`Y_Z][U[UF\\F^G_H`J`L_N^O\\PUP',
	    'PfaX`Y]Z[ZXYVWUUTQTNUJVHXF[E]E`FaG',
	    'PfU[UFZF]G_I`KaOaR`V_X]ZZ[U[',
	    'PfVP]P R`[V[VF`F',
	    'Pf]PVP RV[VF`F',
	    'Pf`G^F[FXGVIUKTOTRUVVXXZ[[][`ZaYaR]R',
	    'PfU[UF RUPaP Ra[aF',
	    'Pf[[[F',
	    'Pf^E^T]W[YXZVZ',
	    'PfUZUE RaZXN RaEUQ',
	    'Pf`[V[VF',
	    'PfT[TF[UbFb[',
	    'PfU[UFa[aF',
	    'PfYF]F_GaIbMbTaX_Z][Y[WZUXTTTMUIWGYF',
	    'PfU[UF]F_G`HaJaM`O_P]QUQ',
	    'Pfc\\a[_Y\\VZUXU RYZWYUWTSTLUHWFYE]E_FaHbLbSaW_Y]ZYZ',
	    'Pfa[ZQ RU[UF]F_G`HaJaM`O_P]QUQ',
	    'PfUZX[][_Z`YaWaU`S_R]QYPWOVNULUJVHWGYF^FaG',
	    'PfUFaF R[[[F',
	    'PfUFUWVYWZY[][_Z`YaWaF',
	    'PfTF[[bF',
	    'PfRFW[[L_[cF',
	    'PfTFb[ RbFT[',
	    'Pf[Q[[ RTF[QbF',
	    'PfTFbFT[b[',
	    'PfaE\\E\\ZaZ',
	    'PfTEbY',
	    'PfUEZEZZUZ',
	    'PfXH[E^H',
	    'PfTZbZ',
	    'PfYE]G',
	    'Pf`[`P_N]MYMWN R`Z^[Y[WZVXVVWTYS^S`R',
	    'PfV[VF RVNXM\\M^N_O`Q`W_Y^Z\\[X[VZ',
	    'Pf_Z][Y[WZVYUWUQVOWNYM]M_N',
	    'Pf_[_F R_Z][Y[WZVYUWUQVOWNYM]M_N',
	    'Pf_Z][Y[WZVXVPWNYM]M_N`P`RVT',
	    'PfWM_M RZ[ZI[G]F_F',
	    'Pf_F_W^Y]Z[[X[VZ R_S]TYTWSVRUPUJVHWGYF]F_G',
	    'PfV[VF R_[_P^N\\MYMWNVO',
	    'PfZ[ZM RZHZI[HZH',
	    'Pf[L[XZZX[W[ R[HZH[I[H',
	    'PfW[WF RYS_[ R_MWU',
	    'Pf][[ZZXZF',
	    'PfS[SM RSOTNVMYM[N[O[[ R[O[N]M`MbNcPc[',
	    'PfVMV[ RVOWNYM\\M^N_P_[',
	    'PfY[WZVYUWUQVOWNYM\\M^N_O`Q`W_Y^Z\\[Y[',
	    'PfVGV\\ RVHXG\\G^H_I`K`Q_S^T\\UXUVT',
	    'Pf_F_[ R_S]TYTWSVRUPUJVHWGYF]F_G',
	    'PfXZXL RXPYNZM\\L^L',
	    'PfVZX[\\[^Z_X_W^U\\TYTWSVQVPWNYM\\M^N',
	    'PfVM^M RYFYXZZ\\[^[',
	    'Pf_M_[ RVMVXWZY[\\[^Z_Y',
	    'PfUMZ[_M',
	    'PfSMW[[Q_[cM',
	    'PfU[`M RUM`[',
	    'PfVF[T R`F[TYYXZV[',
	    'PfUM`MU[`[',
	    'Pf]`\\`Z_Y]YSXQVPXOYMYCZA\\@]@',
	    'Pf[D[Z',
	    'PfY`Z`\\_]]]S^Q`P^O]M]C\\AZ@Y@',
	    'PfVPWOYN]P_O`N',
	    'Pf`C_F^J^T_X`Z R]C[FZJZT[X]Z',
	    'PfYDZF[J[TZXY[ RUDWFXJXTWXU[',
	    'Q\\UTWTYVYXWZUZSXSVUT',
	    'Q\\ZEWEWN',
	    'Q\\SZVZVQ',
	    'Q\\TXVZ',
	    'Q\\TYUYUZTZTY',
	    'Q\\SGZGYOXUUZ RTMYM',
	    'Q\\TKYKXQ RVNVSVUUZ',
	    'Q\\YJSS RWNWZ',
	    'Q\\VJVM RSRSMYMYRXVU[',
	    'Q\\TMXM RVMVW RSWYW',
	    'Q\\SMYM RWIWZUY RWNVRSX',
	    'Q\\SPYMXT RTJVZ',
	    'Q\\TMXMWV RSVZV',
	    'Q\\TKXKXWTW RTQXQ',
	    'Q\\SLTQ RUKVO RYLYRXVUZ',
	    'Q\\SOZO',
	    'Q\\SHZHXN RVKVSUVSZ',
	    'Q\\YEWIULSP RVKVZ',
	    'Q\\VDVH RSOSHYHYOWTUZ',
	    'Q\\TJYJ RVJVX RSXZX',
	    'Q\\SJYJ RWDWZUY RWKVPSX',
	    'Q\\SKYKXZVY RVEVNUTSZ',
	    'Q\\TLXJ RSSZQ RUEXZ',
	    'Q\\UDUJSO RUHYHYOXTTZ',
	    'Q\\UDTKSQ RUIYI RWIWQVVTZ',
	    'Q\\SIYIYXSX',
	    'Q\\SLZL RTETR RXDXRWVUZ',
	    'Q\\THVJ RSNUP RTZWWYRZM',
	    'Q\\THYHXOVTSY RXQZY',
	    'Q\\SMZKXR RUEUZYZ',
	    'Q\\SHUO RYHYOXUU[',
	    'Q\\UDUISO RUGYGYMXUTZ RTLXR',
	    'Q\\XETH RSNZN RVGVOVUTZ',
	    'Q\\SITN RVGWM RYHYPWUTY',
	    'Q\\TGXG RSNYN RVNVSVWTZ',
	    'Q\\VDVZ RVNYR',
	    'Q\\SLYL RVEVPVUTZ',
	    'Q\\TJXJ RSXYX',
	    'Q\\SHZHYOWTTZ RULYW',
	    'Q\\UEWI RSKZKXPWSSV RWSWZ RXRZV',
	    'Q\\YFYMWSSY',
	    'Q\\TJSW RXJZW',
	    'Q\\YJUO RTFTZYZ',
	    'Q\\SIZIYNWUTZ',
	    'Q\\SRVJZW',
	    'Q\\SJYJ RVEVZUZ RTNSU RXNYU',
	    'Q\\SIZIVS RTOXX',
	    'Q\\UFYJ RUMXP RTSYY',
	    'Q\\VGSXYW RWPYY',
	    'Q\\YEYNWSTZ RTKYU',
	    'Q\\TGXG RSOZO RVGVYYY',
	    'Q\\SMYJYQ RTEXZ',
	    'Q\\TIYIXW RSXZX',
	    'Q\\SHYHYXSX RTPYP',
	    'Q\\THXH RSNYNYSWWUZ',
	    'Q\\TFTS RXDXRWVUZ',
	    'Q\\TGTQSZ RXEXYZR',
	    'Q\\TETYWVYO',
	    'Q\\SHSWYWYHSH',
	    'Q\\SOSHYHYOXUUZ',
	    'Q\\SHVL RSZVVYRZK',
	    'Q\\SFUI RVEXH',
	    'Q\\TEUEVFVGUHTHSGSFTE',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'RR',
	    'Pf_V]WYWWVVUUSUMVKWJYI]I_J R^FWY',
	    'PfUM]M RU[`[ RX[XIYG[F^F`G',
	    'PfTObObR',
	    'PfTFbF',
	    'Pf[E[M R[Q[Y',
	    'PfSTcT RSMcM R[Q[[ RTF[QbF',
	    'PfRFW[[L_[cF RROcO',
	    'RR',
	    'Q\\VEVY',
	    'Q\\ZOSOUL RSOUR',
	    'Q\\VYVEYG RVESG',
	    'Q\\SOZOXL RZOXR',
	    'Q\\VEVYSW RVYYW',
	    'Q\\UMWMWPUPUM RVMVO',
	    'Q\\UMWMXNXPWQUQTPTNUM',
	];
	
	return newstroke;
}

var hasRequiredFonts;

function requireFonts () {
	if (hasRequiredFonts) return fonts;
	hasRequiredFonts = 1;
	Object.defineProperty(fonts, "__esModule", { value: true });
	fonts.Font = void 0;
	fonts.getFont = getFont;
	fonts.stringToFontStrokes = stringToFontStrokes;
	const newstroke_1 = requireNewstroke();
	var Font;
	(function (Font) {
	    Font["Newstroke"] = "newstroke";
	})(Font || (fonts.Font = Font = {}));
	function getFont(font) {
	    if (font === Font.Newstroke) {
	        return {
	            startChar: 0x20,
	            heightRatio: 4 / 3,
	            unknownChar: 'F^K[KFYFY[K[',
	            fontRenderScale: 1 / 21,
	            italicTilt: 1 / 8,
	            charSpacing: 0,
	            overbarSpacing: 0.1,
	            underlineSpacing: 0.1,
	            interlineRatio: 1.62,
	            fontRenderOffset: [-'R'.charCodeAt(0), -'R'.charCodeAt(0) + 10 + 3],
	            glyphs: newstroke_1.newstroke_font,
	        };
	    }
	    throw new Error('Unknown font: ' + font);
	}
	function stringToFontStrokes(font, str) {
	    const strokes = [];
	    for (let i = 0; i < str.length; ++i) {
	        const charCode = str.charCodeAt(i);
	        let baseCode = Number.NEGATIVE_INFINITY;
	        for (let code of Object.keys(font.glyphs)) {
	            if (typeof code === 'string') {
	                code = parseInt(code);
	            }
	            if (code > charCode) {
	                break;
	            }
	            baseCode = code;
	        }
	        if (baseCode < 0) {
	            strokes.push(font.unknownChar);
	            continue;
	        }
	        const glyphPack = font.glyphs[baseCode];
	        if (charCode >= baseCode + glyphPack.length) {
	            strokes.push(font.unknownChar);
	            continue;
	        }
	        strokes.push(glyphPack[charCode - baseCode]);
	    }
	    return strokes;
	}
	
	return fonts;
}

var hasRequiredAltiumSheet;

function requireAltiumSheet () {
	if (hasRequiredAltiumSheet) return AltiumSheet;
	hasRequiredAltiumSheet = 1;
	Object.defineProperty(AltiumSheet, "__esModule", { value: true });
	AltiumSheet.AltiumSheet = void 0;
	const CoordinateConverter_1 = requireCoordinateConverter();
	const fonts_1 = requireFonts();
	class AltiumSheet$1 {
	    static { this.FontSizeConversionRatio = 5.4; }
	    static { this.DefaultSnapGridSizeMils = 100; }
	    constructor(record) {
	        this.record = record;
	        this._fonts = AltiumSheet$1.parseFonts(record);
	    }
	    getSnapGridSizeMils() {
	        const snapGridSizeMils = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(this.record.properties, 'snapgridsize');
	        return snapGridSizeMils > 0 ? snapGridSizeMils : AltiumSheet$1.DefaultSnapGridSizeMils;
	    }
	    isSnapGridEnabled() {
	        const snapGridOn = this.record.properties['snapgridon']?.value;
	        return snapGridOn === undefined ? true : CoordinateConverter_1.CoordinateConverter.altiumBool(snapGridOn);
	    }
	    getFonts() {
	        return this._fonts;
	    }
	    getFont(fontId) {
	        const fontIdNum = typeof fontId === 'string' ? parseInt(fontId, 10) : fontId;
	        if (fontIdNum === 0) {
	            return this.getDefaultFont();
	        }
	        const font = this._fonts.get(fontIdNum);
	        if (font === undefined) {
	            throw new Error(`Font not found with ID: ${fontId}`);
	        }
	        return font;
	    }
	    getDefaultFont() {
	        const font1 = this._fonts.get(1);
	        if (font1) {
	            return font1;
	        }
	        return {
	            fontName: fonts_1.Font.Newstroke,
	            size: 1.47,
	            areaColor: { r: 0, g: 0, b: 0 },
	            textOpts: {
	                size: 1.47,
	            },
	        };
	    }
	    static parseFonts(record) {
	        const fonts = new Map();
	        const { fontIdCount, properties } = record;
	        for (let i = 1; i <= fontIdCount; i++) {
	            const fontName = properties[`fontname${i}`]?.value ?? '';
	            const size = CoordinateConverter_1.CoordinateConverter.milsToMm(parseInt(properties[`size${i}`]?.value ?? '10', 10)) *
	                AltiumSheet$1.FontSizeConversionRatio;
	            const rotation = properties[`rotation${i}`]?.value ? parseInt(properties[`rotation${i}`].value, 10) : undefined;
	            const italic = properties[`italic${i}`]?.value ? CoordinateConverter_1.CoordinateConverter.altiumBool(properties[`italic${i}`].value) : undefined;
	            const bold = properties[`bold${i}`]?.value ? CoordinateConverter_1.CoordinateConverter.altiumBool(properties[`bold${i}`].value) : undefined;
	            const underline = properties[`underline${i}`]?.value ?
	                CoordinateConverter_1.CoordinateConverter.altiumBool(properties[`underline${i}`].value)
	                : undefined;
	            const areaColor = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties[`areacolor${i}`]?.value ?? '0', 10));
	            const textAttrs = [];
	            if (italic) {
	                textAttrs.push('italic');
	            }
	            if (bold) {
	                textAttrs.push('bold');
	            }
	            fonts.set(i, {
	                fontName,
	                size,
	                rotation,
	                italic,
	                bold,
	                underline,
	                areaColor,
	                textOpts: {
	                    attr: textAttrs.length > 0 ? textAttrs : undefined,
	                    size,
	                },
	            });
	        }
	        return fonts;
	    }
	}
	AltiumSheet.AltiumSheet = AltiumSheet$1;
	
	return AltiumSheet;
}

var hasRequiredAltiumSchematic;

function requireAltiumSchematic () {
	if (hasRequiredAltiumSchematic) return AltiumSchematic;
	hasRequiredAltiumSchematic = 1;
	Object.defineProperty(AltiumSchematic, "__esModule", { value: true });
	AltiumSchematic.AltiumSchematic = void 0;
	const AltiumComponent_1 = requireAltiumComponent();
	const AltiumHarnessConnector_1 = requireAltiumHarnessConnector();
	const AltiumImplementationList_1 = requireAltiumImplementationList();
	const AltiumSheetSymbol_1 = requireAltiumSheetSymbol();
	const AltiumSheet_1 = requireAltiumSheet();
	const SchDocTypes_1 = requireSchDocTypes();
	class AltiumSchematic$1 {
	    constructor(version, fileName, storage, records, processingResults = []) {
	        this.version = version;
	        this.fileName = fileName;
	        this.storage = storage;
	        this.records = records;
	        this.processingResults = processingResults;
	        this._childRecordsByOwnerIndex = this.buildChildRecordsByOwnerIndex();
	        const allComponents = this.records
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.COMPONENT)
	            .map((r) => new AltiumComponent_1.AltiumComponent(this, r));
	        this._components = this.excludeMaskedComponents(allComponents);
	        this._componentsByIndex = new Map(this._components.map((c) => [c.record.index, c]));
	        this._componentsByUniqueId = new Map(this._components.map((c) => [c.record.uniqueId, c]));
	        this._componentsByDesignator = new Map(this._components.reduce((acc, component) => {
	            const designator = component.getDesignatorText();
	            if (designator) {
	                const existingComponents = acc.get(designator) ?? [];
	                acc.set(designator, [...existingComponents, component]);
	            }
	            return acc;
	        }, new Map()));
	        this._sheetSymbols = this.records
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.SHEET_SYMBOL)
	            .map((r) => new AltiumSheetSymbol_1.AltiumSheetSymbol(this, r));
	        this._sheetSymbolsByIndex = new Map(this._sheetSymbols.map((s) => [s.record.index, s]));
	        this._harnessConnectors = this.records
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.HARNESS_CONNECTOR)
	            .map((r) => new AltiumHarnessConnector_1.AltiumHarnessConnector(this, r));
	        this._harnessConnectorsByIndex = new Map(this._harnessConnectors.map((h) => [h.record.index, h]));
	        this._harnessConnectorChildrenByIndex = this.buildHarnessConnectorChildrenByIndex();
	        this._implementationLists = this.records
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.IMPLEMENTATION_LIST)
	            .map((r) => new AltiumImplementationList_1.AltiumImplementationList(this, r));
	        this._implementationListsByIndex = new Map(this._implementationLists.map((i) => [i.record.index, i]));
	        const sheetRecord = this.records.find((r) => r.recordType === SchDocTypes_1.RecordType.SHEET);
	        this._sheet = sheetRecord ? new AltiumSheet_1.AltiumSheet(sheetRecord) : undefined;
	    }
	    getRecordByIndex(index) {
	        return this.records.find((r) => r.index === index);
	    }
	    getChildRecords(ownerIndex) {
	        return this._childRecordsByOwnerIndex.get(ownerIndex) ?? [];
	    }
	    getComponents() {
	        return this._components;
	    }
	    getComponentByIndex(index) {
	        return this._componentsByIndex.get(index);
	    }
	    getComponentByUniqueId(uniqueId) {
	        return this._componentsByUniqueId.get(uniqueId);
	    }
	    getComponentsByDesignator(designator) {
	        return this._componentsByDesignator.get(designator);
	    }
	    getSheetSymbols() {
	        return this._sheetSymbols;
	    }
	    getSheetSymbolByIndex(index) {
	        const symbol = this._sheetSymbolsByIndex.get(index);
	        if (!symbol) {
	            throw new Error(`Sheet symbol not found with index: ${index}`);
	        }
	        return symbol;
	    }
	    getSheetSymbolByUniqueId(uniqueId) {
	        if (!this._sheetSymbolsByUniqueId) {
	            this._sheetSymbolsByUniqueId = new Map(this._sheetSymbols.map((s) => [s.uniqueId, s]));
	        }
	        const symbol = this._sheetSymbolsByUniqueId.get(uniqueId);
	        if (!symbol) {
	            throw new Error(`Sheet symbol not found with unique ID: ${uniqueId}`);
	        }
	        return symbol;
	    }
	    getWires() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.WIRE);
	    }
	    getBuses() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.BUS);
	    }
	    getBusEntries() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.BUS_ENTRY);
	    }
	    getNetLabels() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.NET_LABEL);
	    }
	    getPowerPorts() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.POWER_PORT);
	    }
	    getJunctions() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.JUNCTION);
	    }
	    getGraphics() {
	        return this.records.filter(SchDocTypes_1.isGraphicRecord);
	    }
	    getTemplates() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.TEMPLATE);
	    }
	    getHarnessConnectors() {
	        return this._harnessConnectors;
	    }
	    getHarnessConnectorByIndex(index) {
	        return this._harnessConnectorsByIndex.get(index);
	    }
	    getHarnessConnectorChildren(connectorIndex) {
	        return this._harnessConnectorChildrenByIndex.get(connectorIndex) ?? this.getChildRecords(connectorIndex);
	    }
	    getHarnessDefinitionEntries(harnessType) {
	        const foldedHarnessType = harnessType.toLowerCase();
	        const connector = this._harnessConnectors.find((h) => h.getHarnessTypeText()?.toLowerCase() === foldedHarnessType);
	        if (!connector) {
	            return [];
	        }
	        const entries = connector.getEntries();
	        const definitionEntries = entries.filter((entry) => this.isAdditionalListHarnessChild(entry));
	        return definitionEntries.length > 0 ? definitionEntries : entries;
	    }
	    getHarnessDefinitionMembers(harnessType) {
	        return this.getHarnessDefinitionEntries(harnessType).map((entry) => entry.name);
	    }
	    getImplementationLists() {
	        return this._implementationLists;
	    }
	    getImplementationListByIndex(index) {
	        const implementationList = this._implementationListsByIndex.get(index);
	        if (!implementationList) {
	            throw new Error(`Implementation list not found with index: ${index}`);
	        }
	        return implementationList;
	    }
	    getPorts() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.PORT);
	    }
	    getSignalHarnesses() {
	        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.SIGNAL_HARNESS);
	    }
	    tryGetSheet() {
	        return this._sheet;
	    }
	    getSheet() {
	        if (!this._sheet) {
	            throw new Error(`No sheet record found in schematic`);
	        }
	        return this._sheet;
	    }
	    getCompileMaskRects() {
	        return this.records
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.COMPILE_MASK)
	            .map((mask) => ({
	            minX: Math.min(mask.location.x, mask.corner.x),
	            maxX: Math.max(mask.location.x, mask.corner.x),
	            minY: Math.min(mask.location.y, mask.corner.y),
	            maxY: Math.max(mask.location.y, mask.corner.y),
	        }));
	    }
	    excludeMaskedComponents(components) {
	        const rects = this.getCompileMaskRects();
	        if (rects.length === 0) {
	            return components;
	        }
	        return components.filter((component) => {
	            const displayMode = component.record.displayMode ?? 0;
	            const activePins = component
	                .getPins()
	                .filter((pin) => (pin.ownerPartId === -1 || pin.ownerPartId === component.record.currentPartId) &&
	                pin.ownerPartDisplayMode !== -1 &&
	                pin.ownerPartDisplayMode === displayMode);
	            const points = [component.record.location, ...activePins.map((pin) => pin.location)];
	            const enclosed = rects.some((rect) => points.every((pt) => pt.x >= rect.minX && pt.x <= rect.maxX && pt.y >= rect.minY && pt.y <= rect.maxY));
	            return !enclosed;
	        });
	    }
	    buildHarnessConnectorChildrenByIndex() {
	        const childrenByConnector = new Map();
	        const connectorsByIndex = new Map();
	        for (const record of this.records) {
	            if (record.recordType === SchDocTypes_1.RecordType.HARNESS_CONNECTOR) {
	                connectorsByIndex.set(record.index, record);
	            }
	        }
	        let currentConnector;
	        for (const record of this.records) {
	            if (record.recordType === SchDocTypes_1.RecordType.HARNESS_CONNECTOR) {
	                currentConnector = record;
	                if (!childrenByConnector.has(record.index)) {
	                    childrenByConnector.set(record.index, []);
	                }
	                continue;
	            }
	            if (record.recordType !== SchDocTypes_1.RecordType.HARNESS_ENTRY && record.recordType !== SchDocTypes_1.RecordType.HARNESS_TYPE) {
	                continue;
	            }
	            const explicitOwner = connectorsByIndex.get(record.ownerIndex);
	            const owner = explicitOwner ?? currentConnector;
	            if (!owner) {
	                continue;
	            }
	            const children = childrenByConnector.get(owner.index) ?? [];
	            children.push(record);
	            childrenByConnector.set(owner.index, children);
	        }
	        return childrenByConnector;
	    }
	    buildChildRecordsByOwnerIndex() {
	        const childrenByOwner = new Map();
	        for (const record of this.records) {
	            const children = childrenByOwner.get(record.ownerIndex) ?? [];
	            children.push(record);
	            childrenByOwner.set(record.ownerIndex, children);
	        }
	        return childrenByOwner;
	    }
	    isAdditionalListHarnessChild(record) {
	        return record.properties.ownerindexadditionallist?.value?.toUpperCase() === 'T';
	    }
	}
	AltiumSchematic.AltiumSchematic = AltiumSchematic$1;
	
	return AltiumSchematic;
}

var RecordParser = {};

var RecordTypes = {};

var hasRequiredRecordTypes;

function requireRecordTypes () {
	if (hasRequiredRecordTypes) return RecordTypes;
	hasRequiredRecordTypes = 1;
	Object.defineProperty(RecordTypes, "__esModule", { value: true });
	RecordTypes.CompileMaskRecordSchema = RecordTypes.TemplateRecordSchema = RecordTypes.ImageRecordSchema = RecordTypes.ImplementationRecordSchema = RecordTypes.ImplementationListRecordSchema = RecordTypes.SignalHarnessRecordSchema = RecordTypes.HarnessTypeRecordSchema = RecordTypes.HarnessEntryRecordSchema = RecordTypes.HarnessConnectorRecordSchema = RecordTypes.RoundRectangleRecordSchema = RecordTypes.EllipticalArcRecordSchema = RecordTypes.EllipseRecordSchema = RecordTypes.PolygonRecordSchema = RecordTypes.ArcRecordSchema = RecordTypes.BezierRecordSchema = RecordTypes.FileNameRecordSchema = RecordTypes.SheetNameRecordSchema = RecordTypes.LabelRecordSchema = RecordTypes.SheetRecordSchema = RecordTypes.PortRecordSchema = RecordTypes.SheetEntryRecordSchema = RecordTypes.SheetSymbolRecordSchema = RecordTypes.JunctionRecordSchema = RecordTypes.TextFrameRecordSchema = RecordTypes.ParameterRecordSchema = RecordTypes.PowerPortRecordSchema = RecordTypes.DesignatorRecordSchema = RecordTypes.PolylineRecordSchema = RecordTypes.RectangleRecordSchema = RecordTypes.LineRecordSchema = RecordTypes.NetLabelRecordSchema = RecordTypes.BusEntryRecordSchema = RecordTypes.BusRecordSchema = RecordTypes.WireRecordSchema = RecordTypes.PinRecordSchema = RecordTypes.ComponentRecordSchema = RecordTypes.HeaderRecordSchema = void 0;
	RecordTypes.isHeaderRecord = isHeaderRecord;
	const zod_1 = /*@__PURE__*/ requireZod();
	const SchDocTypes_1 = requireSchDocTypes();
	const CoordinateConverter_1 = requireCoordinateConverter();
	const AltiumEnums_1 = requireAltiumEnums();
	const PropertyParser_1 = requirePropertyParser();
	RecordTypes.HeaderRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.HEADER),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    majorVersion: zod_1.z.string(),
	    minorVersion: zod_1.z.string(),
	    header: zod_1.z.string(),
	})
	    .strict();
	function isHeaderRecord(record) {
	    return record.recordType === SchDocTypes_1.RecordType.HEADER;
	}
	RecordTypes.ComponentRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.COMPONENT),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    allPinCount: zod_1.z.number().optional(),
	    componentDescription: zod_1.z.string(),
	    componentkind: AltiumEnums_1.ComponentKindSchema.optional(),
	    unrecognizedComponentKind: zod_1.z.number().optional(),
	    currentPartId: zod_1.z.number(),
	    designItemId: zod_1.z.string().optional(),
	    displayMode: zod_1.z.number(),
	    displayModeCount: zod_1.z.number(),
	    isMirrored: zod_1.z.boolean(),
	    itemGuid: zod_1.z.string().optional(),
	    libReference: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    orientation: AltiumEnums_1.OrientationSchema,
	    ownerPartId: zod_1.z.string().optional(),
	    partCount: zod_1.z.number(),
	    partIdLocked: zod_1.z.boolean().optional(),
	    revisionGuid: zod_1.z.string().optional(),
	    sourceLibraryName: zod_1.z.string(),
	    symbolItemGuid: zod_1.z.string().optional(),
	    symbolRevisionGuid: zod_1.z.string().optional(),
	    symbolVaultGuid: zod_1.z.string().optional(),
	    targetFileName: zod_1.z.string().optional(),
	    uniqueId: zod_1.z.string(),
	    vaultGuid: zod_1.z.string().optional(),
	})
	    .strict();
	RecordTypes.PinRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.PIN),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    name: zod_1.z.string(),
	    designator: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    pinLength: zod_1.z.number(),
	    orientation: AltiumEnums_1.OrientationSchema,
	    electrical: AltiumEnums_1.ElectricalTypeSchema,
	    hidden: zod_1.z.boolean(),
	    showPinName: zod_1.z.boolean(),
	    showDesignator: zod_1.z.boolean(),
	    locked: zod_1.z.boolean(),
	    ownerPartId: zod_1.z.number(),
	    ownerPartDisplayMode: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.WireRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.WIRE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    points: zod_1.z.array(CoordinateConverter_1.AltiumCoordinateSchema),
	    lineWidth: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.BusRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.BUS),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    points: zod_1.z.array(CoordinateConverter_1.AltiumCoordinateSchema),
	    lineWidth: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.BusEntryRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.BUS_ENTRY),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    corner: CoordinateConverter_1.AltiumCoordinateSchema,
	})
	    .strict();
	RecordTypes.NetLabelRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.NET_LABEL),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    text: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    orientation: AltiumEnums_1.OrientationSchema,
	    fontId: zod_1.z.number(),
	    justification: AltiumEnums_1.LabelJustificationSchema,
	})
	    .strict();
	RecordTypes.LineRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.LINE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    startLocation: CoordinateConverter_1.AltiumCoordinateSchema,
	    endLocation: CoordinateConverter_1.AltiumCoordinateSchema,
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.RectangleRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.RECTANGLE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    topLeft: CoordinateConverter_1.AltiumCoordinateSchema,
	    bottomRight: CoordinateConverter_1.AltiumCoordinateSchema,
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    fillColor: AltiumEnums_1.ColorSchema,
	    isSolid: zod_1.z.boolean(),
	    isTransparent: zod_1.z.boolean(),
	    isNotAccesible: zod_1.z.boolean(),
	    indexInSheet: zod_1.z.number(),
	    ownerPartId: zod_1.z.number(),
	    ownerPartDisplayMode: zod_1.z.number(),
	    uniqueId: zod_1.z.string(),
	})
	    .strict();
	RecordTypes.PolylineRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.POLYLINE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    points: zod_1.z.array(CoordinateConverter_1.AltiumCoordinateSchema),
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    isNotAccesible: zod_1.z.boolean(),
	    indexInSheet: zod_1.z.number(),
	    ownerPartId: zod_1.z.number(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	    lineShapeSize: zod_1.z.number(),
	    uniqueId: zod_1.z.string(),
	})
	    .strict();
	RecordTypes.DesignatorRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.DESIGNATOR),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    name: zod_1.z.string(),
	    text: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    orientation: AltiumEnums_1.OrientationSchema,
	    justification: AltiumEnums_1.JustificationSchema,
	    fontId: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.PowerPortRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.POWER_PORT),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    text: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    orientation: AltiumEnums_1.PortOrientationSchema,
	    style: AltiumEnums_1.PowerPortStyleSchema,
	    showNetName: zod_1.z.boolean(),
	    isCrossSheetConnector: zod_1.z.boolean(),
	})
	    .strict();
	RecordTypes.ParameterRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.PARAMETER),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    name: zod_1.z.string(),
	    text: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    orientation: AltiumEnums_1.OrientationSchema,
	    justification: AltiumEnums_1.JustificationSchema,
	    isHidden: zod_1.z.boolean(),
	    isMirrored: zod_1.z.boolean(),
	    isShowName: zod_1.z.boolean(),
	    fontId: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.TextFrameRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.TEXT_FRAME),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    corner: CoordinateConverter_1.AltiumCoordinateSchema,
	    text: zod_1.z.string(),
	    fontId: zod_1.z.number(),
	    isWordWrapped: zod_1.z.boolean(),
	    clipToRect: zod_1.z.boolean(),
	    showBorder: zod_1.z.boolean(),
	    textMargin: zod_1.z.number(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    textColor: AltiumEnums_1.ColorSchema,
	    borderColor: AltiumEnums_1.ColorSchema,
	    borderWidth: zod_1.z.number(),
	    isSolid: zod_1.z.boolean(),
	    alignment: AltiumEnums_1.TextFrameAlignmentSchema,
	})
	    .strict();
	RecordTypes.JunctionRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.JUNCTION),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	})
	    .strict();
	RecordTypes.SheetSymbolRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.SHEET_SYMBOL),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    xSize: zod_1.z.number(),
	    ySize: zod_1.z.number(),
	    isSolid: zod_1.z.boolean(),
	    color: AltiumEnums_1.ColorSchema,
	    areaColor: AltiumEnums_1.ColorSchema,
	    lineWidth: AltiumEnums_1.LineWidthSchema,
	    uniqueId: zod_1.z.string(),
	})
	    .strict();
	RecordTypes.SheetEntryRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.SHEET_ENTRY),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    distanceFromTop: zod_1.z.number(),
	    side: AltiumEnums_1.SheetSymbolSideSchema,
	    name: zod_1.z.string(),
	    harnessType: zod_1.z.string(),
	    ioType: AltiumEnums_1.PortIOTypeSchema,
	    style: AltiumEnums_1.PortStyleSchema,
	    textFontId: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.PortRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.PORT),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    name: zod_1.z.string(),
	    harnessType: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    width: zod_1.z.number(),
	    height: zod_1.z.number(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    color: AltiumEnums_1.ColorSchema,
	    textColor: AltiumEnums_1.ColorSchema,
	    fontId: zod_1.z.number(),
	    alignment: AltiumEnums_1.PortAlignmentSchema,
	    ioType: AltiumEnums_1.PortIOTypeSchema,
	    style: AltiumEnums_1.PortStyleSchema,
	})
	    .strict();
	RecordTypes.SheetRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.SHEET),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    fontIdCount: zod_1.z.number(),
	    useCustomSheet: zod_1.z.boolean(),
	    customX: zod_1.z.number(),
	    customY: zod_1.z.number(),
	    sheetStyle: zod_1.z.number(),
	    workspaceOrientation: zod_1.z.number(),
	})
	    .passthrough();
	RecordTypes.LabelRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.LABEL),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    text: zod_1.z.string(),
	    fontId: zod_1.z.number(),
	    isMirrored: zod_1.z.boolean(),
	    justification: AltiumEnums_1.LabelJustificationSchema,
	    orientation: AltiumEnums_1.OrientationSchema,
	    ownerPartId: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.SheetNameRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.SHEET_NAME),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    text: zod_1.z.string(),
	    orientation: AltiumEnums_1.OrientationSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    isHidden: zod_1.z.boolean(),
	    fontId: zod_1.z.coerce.number(),
	})
	    .strict();
	RecordTypes.FileNameRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.FILE_NAME),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    text: zod_1.z.string(),
	    orientation: AltiumEnums_1.OrientationSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    isHidden: zod_1.z.boolean(),
	    fontId: zod_1.z.coerce.number(),
	})
	    .strict();
	RecordTypes.BezierRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.BEZIER),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    points: zod_1.z.array(CoordinateConverter_1.AltiumCoordinateSchema),
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.ArcRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.ARC),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    center: CoordinateConverter_1.AltiumCoordinateSchema,
	    radius: zod_1.z.number(),
	    startAngle: zod_1.z.number(),
	    endAngle: zod_1.z.number(),
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    isSolid: zod_1.z.boolean(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.PolygonRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.POLYGON),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    points: zod_1.z.array(CoordinateConverter_1.AltiumCoordinateSchema),
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    isSolid: zod_1.z.boolean(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.EllipseRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.ELLIPSE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    center: CoordinateConverter_1.AltiumCoordinateSchema,
	    radius: zod_1.z.number(),
	    secondaryRadius: zod_1.z.number(),
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    isSolid: zod_1.z.boolean(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.EllipticalArcRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.ELLIPTICAL_ARC),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    center: CoordinateConverter_1.AltiumCoordinateSchema,
	    radius: zod_1.z.number(),
	    secondaryRadius: zod_1.z.number(),
	    startAngle: zod_1.z.number(),
	    endAngle: zod_1.z.number(),
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    isSolid: zod_1.z.boolean(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.RoundRectangleRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.ROUND_RECTANGLE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    corner: CoordinateConverter_1.AltiumCoordinateSchema,
	    cornerRadius: CoordinateConverter_1.AltiumCoordinateSchema,
	    lineWidth: zod_1.z.number(),
	    color: AltiumEnums_1.ColorSchema,
	    isSolid: zod_1.z.boolean(),
	    areaColor: AltiumEnums_1.ColorSchema,
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	    transparent: zod_1.z.boolean(),
	    isNotAccesible: zod_1.z.boolean(),
	    lineStyle: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.HarnessConnectorRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.HARNESS_CONNECTOR),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    xSize: zod_1.z.number(),
	    ySize: zod_1.z.number(),
	    lineWidth: zod_1.z.number(),
	    isSolid: zod_1.z.boolean(),
	    side: AltiumEnums_1.SheetSymbolSideSchema,
	    primaryConnectionPosition: zod_1.z.union([zod_1.z.number(), zod_1.z.nan()]),
	    color: AltiumEnums_1.ColorSchema,
	    areaColor: AltiumEnums_1.ColorSchema,
	})
	    .strict();
	RecordTypes.HarnessEntryRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.HARNESS_ENTRY),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    distanceFromTop: zod_1.z.number(),
	    side: AltiumEnums_1.SheetSymbolSideSchema,
	    name: zod_1.z.string(),
	    harnessType: zod_1.z.string(),
	    color: AltiumEnums_1.ColorSchema,
	    areaColor: AltiumEnums_1.ColorSchema,
	    textColor: AltiumEnums_1.ColorSchema,
	    textFontId: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.HarnessTypeRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.HARNESS_TYPE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    text: zod_1.z.string(),
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    isHidden: zod_1.z.boolean(),
	    color: AltiumEnums_1.ColorSchema,
	    fontId: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.SignalHarnessRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.SIGNAL_HARNESS),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    points: zod_1.z.array(CoordinateConverter_1.AltiumCoordinateSchema),
	    color: AltiumEnums_1.ColorSchema,
	    lineWidth: zod_1.z.number(),
	})
	    .strict();
	RecordTypes.ImplementationListRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.IMPLEMENTATION_LIST),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	})
	    .strict();
	RecordTypes.ImplementationRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.IMPLEMENTATION),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    modelName: zod_1.z.string(),
	    modelType: zod_1.z.string(),
	    dataFile: zod_1.z.string(),
	    description: zod_1.z.string(),
	    isCurrent: zod_1.z.boolean(),
	})
	    .strict();
	RecordTypes.ImageRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.IMAGE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    corner: CoordinateConverter_1.AltiumCoordinateSchema,
	    filename: zod_1.z.string(),
	    ownerPartId: zod_1.z.number().optional(),
	    ownerPartDisplayMode: zod_1.z.number().optional(),
	})
	    .strict();
	RecordTypes.TemplateRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.TEMPLATE),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    filename: zod_1.z.string(),
	    isNotAccesible: zod_1.z.boolean(),
	})
	    .strict();
	RecordTypes.CompileMaskRecordSchema = zod_1.z
	    .object({
	    recordType: zod_1.z.literal(SchDocTypes_1.RecordType.COMPILE_MASK),
	    index: zod_1.z.number(),
	    ownerIndex: zod_1.z.number(),
	    properties: PropertyParser_1.AltiumPropertiesSchema,
	    location: CoordinateConverter_1.AltiumCoordinateSchema,
	    corner: CoordinateConverter_1.AltiumCoordinateSchema,
	})
	    .strict();
	
	return RecordTypes;
}

var hasRequiredRecordParser;

function requireRecordParser () {
	if (hasRequiredRecordParser) return RecordParser;
	hasRequiredRecordParser = 1;
	Object.defineProperty(RecordParser, "__esModule", { value: true });
	RecordParser.RecordParser = void 0;
	const SchDocTypes_1 = requireSchDocTypes();
	const CoordinateConverter_1 = requireCoordinateConverter();
	const AltiumEnums_1 = requireAltiumEnums();
	const RecordTypes_1 = requireRecordTypes();
	function convertTildeOneToNewline(text) {
	    return text.replace(/~1/g, '\n');
	}
	function decodeNetLabelText(text) {
	    return convertTildeOneToNewline(text).replace(/¦/g, '|');
	}
	class RecordParser$1 {
	    static parseRecord(recordType, index, ownerIndex, properties) {
	        switch (recordType) {
	            case SchDocTypes_1.RecordType.HEADER:
	                return this.parseHeader(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.COMPONENT:
	                return this.parseComponent(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.PIN:
	                return this.parsePin(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.LABEL:
	                return this.parseLabel(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.WIRE:
	                return this.parseWire(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.BUS:
	                return this.parseBus(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.BUS_ENTRY:
	                return this.parseBusEntry(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.NET_LABEL:
	                return this.parseNetLabel(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.LINE:
	                return this.parseLine(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.RECTANGLE:
	                return this.parseRectangle(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.SHEET_SYMBOL:
	                return this.parseSheetSymbol(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.SHEET_ENTRY:
	                return this.parseSheetEntry(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.POWER_PORT:
	                return this.parsePowerPort(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.PORT:
	                return this.parsePort(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.POLYLINE:
	                return this.parsePolyline(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.JUNCTION:
	                return this.parseJunction(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.TEXT_FRAME:
	                return this.parseTextFrame(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.SHEET:
	                return this.parseSheet(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.SHEET_NAME:
	                return this.parseSheetName(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.FILE_NAME:
	                return this.parseFileName(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.DESIGNATOR:
	                return this.parseDesignator(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.PARAMETER:
	                return this.parseParameter(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.BEZIER:
	                return this.parseBezier(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.ARC:
	                return this.parseArc(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.POLYGON:
	                return this.parsePolygon(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.ELLIPSE:
	                return this.parseEllipse(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.ELLIPTICAL_ARC:
	                return this.parseEllipticalArc(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.ROUND_RECTANGLE:
	                return this.parseRoundRectangle(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.HARNESS_CONNECTOR:
	                return this.parseHarnessConnector(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.HARNESS_ENTRY:
	                return this.parseHarnessEntry(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.HARNESS_TYPE:
	                return this.parseHarnessType(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.SIGNAL_HARNESS:
	                return this.parseSignalHarness(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.IMPLEMENTATION_LIST:
	                return this.parseImplementationList(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.IMPLEMENTATION:
	                return this.parseImplementation(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.IMAGE:
	                return this.parseImage(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.TEMPLATE:
	                return this.parseTemplate(index, ownerIndex, properties);
	            case SchDocTypes_1.RecordType.COMPILE_MASK:
	                return this.parseCompileMask(index, ownerIndex, properties);
	            default:
	                return this.parseUnknown(recordType, index, ownerIndex, properties);
	        }
	    }
	    static parseStrictInteger(value) {
	        if (value === undefined || !/^[+-]?\d+$/.test(value.trim())) {
	            return NaN;
	        }
	        return parseInt(value, 10);
	    }
	    static parseHeader(index, ownerIndex, properties) {
	        const record = {
	            recordType: SchDocTypes_1.RecordType.HEADER,
	            index,
	            ownerIndex,
	            properties,
	            majorVersion: '',
	            minorVersion: properties['minorversion']?.value ?? '',
	            header: properties['header']?.value ?? '',
	        };
	        const majorVersionMatch = /Version\s+(\d+\.\d+)/.exec(record.header);
	        if (majorVersionMatch) {
	            record.majorVersion = majorVersionMatch[1];
	        }
	        return RecordTypes_1.HeaderRecordSchema.parse(record);
	    }
	    static parseComponentKind(value) {
	        if (value === undefined) {
	            return undefined;
	        }
	        const parsed = this.parseStrictInteger(value);
	        if (Number.isNaN(parsed)) {
	            return parsed;
	        }
	        return (0, AltiumEnums_1.isKnownComponentKind)(parsed) ? parsed : AltiumEnums_1.ComponentKind.Standard;
	    }
	    static getUnrecognizedComponentKind(value) {
	        if (value === undefined) {
	            return undefined;
	        }
	        const parsed = this.parseStrictInteger(value);
	        return Number.isNaN(parsed) || (0, AltiumEnums_1.isKnownComponentKind)(parsed) ? undefined : parsed;
	    }
	    static parseComponent(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.COMPONENT,
	            index,
	            ownerIndex,
	            properties,
	            uniqueId: properties['uniqueid']?.value ?? '',
	            libReference: properties['libreference']?.value ?? '',
	            sourceLibraryName: properties['sourcelibraryname']?.value ?? '',
	            componentDescription: properties['componentdescription']?.value ?? '',
	            componentkind: this.parseComponentKind(properties['componentkind']?.value),
	            unrecognizedComponentKind: this.getUnrecognizedComponentKind(properties['componentkind']?.value),
	            allPinCount: parseInt(properties['allpincount']?.value ?? '0', 10),
	            designItemId: properties['designitemid']?.value,
	            itemGuid: properties['itemguid']?.value,
	            revisionGuid: properties['revisionguid']?.value,
	            symbolItemGuid: properties['symbolitemguid']?.value,
	            symbolRevisionGuid: properties['symbolrevisionguid']?.value,
	            symbolVaultGuid: properties['symbolvaultguid']?.value,
	            vaultGuid: properties['vaultguid']?.value,
	            targetFileName: properties['targetfilename']?.value,
	            location,
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            isMirrored: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ismirrored']?.value),
	            partCount: parseInt(properties['partcount']?.value ?? '1', 10),
	            displayModeCount: parseInt(properties['displaymodecount']?.value ?? '0', 10),
	            displayMode: parseInt(properties['displaymode']?.value ?? '0', 10),
	            currentPartId: parseInt(properties['currentpartid']?.value ?? '0', 10),
	            ownerPartId: properties['ownerpartid']?.value,
	            partIdLocked: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['partidlocked']?.value),
	        };
	        return RecordTypes_1.ComponentRecordSchema.parse(record);
	    }
	    static parsePin(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const pinLength = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'pinlength');
	        const pinconglomerate = parseInt(properties['pinconglomerate']?.value ?? '0', 10);
	        const orientation = (pinconglomerate & 0x03);
	        const hidden = (pinconglomerate & 0x04) !== 0;
	        const showPinName = (pinconglomerate & 0x08) !== 0;
	        const showDesignator = (pinconglomerate & 0x10) !== 0;
	        const locked = (pinconglomerate & 0x40) !== 0;
	        const record = {
	            recordType: SchDocTypes_1.RecordType.PIN,
	            index,
	            ownerIndex,
	            properties,
	            name: properties['name']?.value ?? '',
	            designator: properties['designator']?.value ?? '',
	            location,
	            pinLength,
	            orientation,
	            electrical: parseInt(properties['electrical']?.value ?? '0', 10),
	            hidden,
	            showPinName,
	            showDesignator,
	            locked,
	            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
	            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.PinRecordSchema.parse(record);
	    }
	    static parseWire(index, ownerIndex, properties) {
	        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
	        const points = [];
	        for (let i = 1; i <= locationCount; i++) {
	            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
	        }
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.WIRE,
	            index,
	            ownerIndex,
	            properties,
	            points,
	            lineWidth,
	        };
	        return RecordTypes_1.WireRecordSchema.parse(record);
	    }
	    static parseBus(index, ownerIndex, properties) {
	        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
	        const points = [];
	        for (let i = 1; i <= locationCount; i++) {
	            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
	        }
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.BUS,
	            index,
	            ownerIndex,
	            properties,
	            points,
	            lineWidth,
	        };
	        return RecordTypes_1.BusRecordSchema.parse(record);
	    }
	    static parseBusEntry(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.BUS_ENTRY,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            corner,
	        };
	        return RecordTypes_1.BusEntryRecordSchema.parse(record);
	    }
	    static parseNetLabel(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.NET_LABEL,
	            index,
	            ownerIndex,
	            properties,
	            text: decodeNetLabelText(properties['text']?.value ?? ''),
	            location,
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
	            justification: parseInt(properties['justification']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.NetLabelRecordSchema.parse(record);
	    }
	    static parseLine(index, ownerIndex, properties) {
	        const startLocation = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const endLocation = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
	        const color = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10));
	        const record = {
	            recordType: SchDocTypes_1.RecordType.LINE,
	            index,
	            ownerIndex,
	            properties,
	            startLocation,
	            endLocation,
	            lineWidth,
	            color,
	            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
	            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.LineRecordSchema.parse(record);
	    }
	    static parseRectangle(index, ownerIndex, properties) {
	        const topLeft = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const bottomRight = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
	        const color = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10));
	        const fillColor = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10));
	        const record = {
	            recordType: SchDocTypes_1.RecordType.RECTANGLE,
	            index,
	            ownerIndex,
	            properties,
	            topLeft,
	            bottomRight,
	            lineWidth,
	            color,
	            fillColor,
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            isTransparent: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['transparent']?.value),
	            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
	            indexInSheet: parseInt(properties['indexinsheet']?.value ?? '0', 10),
	            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
	            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
	            uniqueId: properties['uniqueid']?.value ?? '',
	        };
	        return RecordTypes_1.RectangleRecordSchema.parse(record);
	    }
	    static parsePolyline(index, ownerIndex, properties) {
	        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
	        const points = [];
	        for (let i = 1; i <= locationCount; i++) {
	            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
	        }
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
	        const color = CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10));
	        const record = {
	            recordType: SchDocTypes_1.RecordType.POLYLINE,
	            index,
	            ownerIndex,
	            properties,
	            points,
	            lineWidth,
	            color,
	            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
	            indexInSheet: parseInt(properties['indexinsheet']?.value ?? '0', 10),
	            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
	            lineShapeSize: parseInt(properties['lineshapesize']?.value ?? '0', 10),
	            uniqueId: properties['uniqueid']?.value ?? '',
	        };
	        return RecordTypes_1.PolylineRecordSchema.parse(record);
	    }
	    static parseDesignator(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.DESIGNATOR,
	            index,
	            ownerIndex,
	            properties,
	            name: convertTildeOneToNewline(properties['name']?.value ?? ''),
	            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
	            location,
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            justification: parseInt(properties['justification']?.value ?? '0', 10),
	            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.DesignatorRecordSchema.parse(record);
	    }
	    static parsePowerPort(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.POWER_PORT,
	            index,
	            ownerIndex,
	            properties,
	            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
	            location,
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            style: parseInt(properties['style']?.value ?? '0', 10),
	            showNetName: properties['shownetname']?.value !== 'F',
	            isCrossSheetConnector: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['iscrosssheetconnector']?.value),
	        };
	        return RecordTypes_1.PowerPortRecordSchema.parse(record);
	    }
	    static parseParameter(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.PARAMETER,
	            index,
	            ownerIndex,
	            properties,
	            name: convertTildeOneToNewline(properties['name']?.value ?? ''),
	            text: decodeNetLabelText(properties['text']?.value ?? ''),
	            location,
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            justification: parseInt(properties['justification']?.value ?? '0', 10),
	            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
	            isMirrored: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ismirrored']?.value),
	            isShowName: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isshowname']?.value),
	            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.ParameterRecordSchema.parse(record);
	    }
	    static parseJunction(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.JUNCTION,
	            index,
	            ownerIndex,
	            properties,
	            location,
	        };
	        return RecordTypes_1.JunctionRecordSchema.parse(record);
	    }
	    static parseTextFrame(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const textMargin = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'textmargin');
	        const borderWidth = CoordinateConverter_1.CoordinateConverter.parseFractionalValue(properties, 'linewidth');
	        const text = properties['text']?.value ?? '';
	        const record = {
	            recordType: SchDocTypes_1.RecordType.TEXT_FRAME,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            corner,
	            text: text.replace(/~1/g, '\n').replace(/~2/g, '|'),
	            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
	            isWordWrapped: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['wordwrap']?.value),
	            clipToRect: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['cliptorect']?.value),
	            showBorder: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['showborder']?.value),
	            textMargin,
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '0', 10)),
	            textColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['textcolor']?.value ?? '0', 10)),
	            borderColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            borderWidth,
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            alignment: parseInt(properties['alignment']?.value ?? '1', 10),
	        };
	        return RecordTypes_1.TextFrameRecordSchema.parse(record);
	    }
	    static parseSheetSymbol(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const xSize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'xsize');
	        const ySize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'ysize');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.SHEET_SYMBOL,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            xSize,
	            ySize,
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '0', 10)),
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            uniqueId: properties['uniqueid']?.value ?? '',
	        };
	        return RecordTypes_1.SheetSymbolRecordSchema.parse(record);
	    }
	    static parseSheetEntry(index, ownerIndex, properties) {
	        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'distancefromtop');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.SHEET_ENTRY,
	            index,
	            ownerIndex,
	            properties,
	            distanceFromTop,
	            side: parseInt(properties['side']?.value ?? '0', 10),
	            name: properties['name']?.value ?? '',
	            harnessType: properties['harnesstype']?.value ?? '',
	            ioType: parseInt(properties['iotype']?.value ?? '0', 10),
	            style: parseInt(properties['style']?.value ?? '0', 10),
	            textFontId: parseInt(properties['textfontid']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.SheetEntryRecordSchema.parse(record);
	    }
	    static parsePort(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const width = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'width');
	        const height = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'height');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.PORT,
	            index,
	            ownerIndex,
	            properties,
	            name: properties['name']?.value ?? '',
	            harnessType: properties['harnesstype']?.value ?? '',
	            location,
	            width,
	            height,
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '0', 10)),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            textColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['textcolor']?.value ?? '0', 10)),
	            fontId: parseInt(properties['textfontid']?.value ?? '0', 10),
	            alignment: parseInt(properties['alignment']?.value ?? '0', 10),
	            ioType: parseInt(properties['iotype']?.value ?? '0', 10),
	            style: parseInt(properties['style']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.PortRecordSchema.parse(record);
	    }
	    static parseSheet(index, ownerIndex, properties) {
	        const customX = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'customx');
	        const customY = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'customy');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.SHEET,
	            index,
	            ownerIndex,
	            properties,
	            fontIdCount: parseInt(properties['fontidcount']?.value ?? '0', 10),
	            useCustomSheet: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['usecustomsheet']?.value),
	            customX,
	            customY,
	            sheetStyle: parseInt(properties['sheetstyle']?.value ?? '0', 10),
	            workspaceOrientation: parseInt(properties['workspaceorientation']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.SheetRecordSchema.parse(record);
	    }
	    static parseLabel(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.LABEL,
	            index,
	            ownerIndex,
	            properties,
	            location: { x: location.x, y: -location.y },
	            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
	            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
	            isMirrored: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ismirrored']?.value),
	            justification: parseInt(properties['justification']?.value ?? '0', 10),
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	        };
	        return RecordTypes_1.LabelRecordSchema.parse(record);
	    }
	    static parseSheetName(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.SHEET_NAME,
	            index,
	            ownerIndex,
	            properties,
	            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
	            fontId: parseInt(properties['fontid']?.value ?? '1', 10),
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            location,
	            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
	        };
	        return RecordTypes_1.SheetNameRecordSchema.parse(record);
	    }
	    static parseFileName(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.FILE_NAME,
	            index,
	            ownerIndex,
	            properties,
	            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
	            fontId: parseInt(properties['fontid']?.value ?? '1', 10),
	            orientation: parseInt(properties['orientation']?.value ?? '0', 10),
	            location,
	            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
	        };
	        return RecordTypes_1.FileNameRecordSchema.parse(record);
	    }
	    static parseBezier(index, ownerIndex, properties) {
	        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
	        const points = [];
	        for (let i = 1; i <= locationCount; i++) {
	            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
	        }
	        const record = {
	            recordType: SchDocTypes_1.RecordType.BEZIER,
	            index,
	            ownerIndex,
	            properties,
	            points,
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
	        };
	        return RecordTypes_1.BezierRecordSchema.parse(record);
	    }
	    static parseArc(index, ownerIndex, properties) {
	        const center = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const radius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'radius');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.ARC,
	            index,
	            ownerIndex,
	            properties,
	            center,
	            radius,
	            startAngle: parseFloat(properties['startangle']?.value ?? '0'),
	            endAngle: parseFloat(properties['endangle']?.value ?? '0'),
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
	        };
	        return RecordTypes_1.ArcRecordSchema.parse(record);
	    }
	    static parsePolygon(index, ownerIndex, properties) {
	        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
	        const points = [];
	        for (let i = 1; i <= locationCount; i++) {
	            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
	        }
	        const record = {
	            recordType: SchDocTypes_1.RecordType.POLYGON,
	            index,
	            ownerIndex,
	            properties,
	            points,
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
	        };
	        return RecordTypes_1.PolygonRecordSchema.parse(record);
	    }
	    static parseEllipse(index, ownerIndex, properties) {
	        const center = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const radius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'radius');
	        const secondaryRadius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'secondaryradius');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.ELLIPSE,
	            index,
	            ownerIndex,
	            properties,
	            center,
	            radius,
	            secondaryRadius,
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
	        };
	        return RecordTypes_1.EllipseRecordSchema.parse(record);
	    }
	    static parseEllipticalArc(index, ownerIndex, properties) {
	        const center = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const radius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'radius');
	        const secondaryRadius = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'secondaryradius');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.ELLIPTICAL_ARC,
	            index,
	            ownerIndex,
	            properties,
	            center,
	            radius,
	            secondaryRadius,
	            startAngle: parseFloat(properties['startangle']?.value ?? '0'),
	            endAngle: parseFloat(properties['endangle']?.value ?? '0'),
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
	        };
	        return RecordTypes_1.EllipticalArcRecordSchema.parse(record);
	    }
	    static parseRoundRectangle(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const cornerRadiusX = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'cornerxradius');
	        const cornerRadiusY = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'corneryradius');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.ROUND_RECTANGLE,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            corner,
	            cornerRadius: { x: cornerRadiusX, y: cornerRadiusY },
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            isSolid: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid']?.value),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	            ownerPartId: properties['ownerpartid']?.value ? parseInt(properties['ownerpartid'].value, 10) : undefined,
	            ownerPartDisplayMode: properties['ownerpartdisplaymode']?.value ? parseInt(properties['ownerpartdisplaymode'].value, 10) : undefined,
	            transparent: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['transparent']?.value),
	            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
	            lineStyle: parseInt(properties['linestyle']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.RoundRectangleRecordSchema.parse(record);
	    }
	    static parseHarnessConnector(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const xSize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'xsize');
	        const ySize = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'ysize');
	        const rawLineWidth = parseInt(properties['linewidth']?.value ?? '0', 10);
	        const rawSide = properties['harnessconnectorside']?.value;
	        const side = RecordParser$1.parseHarnessConnectorSide(rawSide);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.HARNESS_CONNECTOR,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            xSize,
	            ySize,
	            lineWidth: Number.isNaN(rawLineWidth) ? 0 : rawLineWidth,
	            isSolid: properties['issolid']?.value === undefined ? true : CoordinateConverter_1.CoordinateConverter.altiumBool(properties['issolid'].value),
	            side,
	            primaryConnectionPosition: CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'primaryconnectionposition'),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	        };
	        return RecordTypes_1.HarnessConnectorRecordSchema.parse(record);
	    }
	    static parseHarnessEntry(index, ownerIndex, properties) {
	        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(properties, 'distancefromtop');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.HARNESS_ENTRY,
	            index,
	            ownerIndex,
	            properties,
	            distanceFromTop,
	            side: parseInt(properties['side']?.value ?? '0', 10),
	            name: properties['name']?.value ?? '',
	            harnessType: properties['harnesstype']?.value ?? '',
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            areaColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['areacolor']?.value ?? '16777215', 10)),
	            textColor: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['textcolor']?.value ?? '0', 10)),
	            textFontId: parseInt(properties['textfontid']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.HarnessEntryRecordSchema.parse(record);
	    }
	    static parseHarnessType(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const record = {
	            recordType: SchDocTypes_1.RecordType.HARNESS_TYPE,
	            index,
	            ownerIndex,
	            properties,
	            text: convertTildeOneToNewline(properties['text']?.value ?? ''),
	            location,
	            isHidden: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['ishidden']?.value),
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            fontId: parseInt(properties['fontid']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.HarnessTypeRecordSchema.parse(record);
	    }
	    static parseHarnessConnectorSide(rawSide) {
	        switch (parseInt(rawSide ?? '0', 10)) {
	            case 1:
	                return AltiumEnums_1.SheetSymbolSide.Right;
	            case 2:
	                return AltiumEnums_1.SheetSymbolSide.Top;
	            case 3:
	                return AltiumEnums_1.SheetSymbolSide.Bottom;
	            case 0:
	            default:
	                return AltiumEnums_1.SheetSymbolSide.Left;
	        }
	    }
	    static parseSignalHarness(index, ownerIndex, properties) {
	        const locationCount = parseInt(properties['locationcount']?.value ?? '0', 10);
	        const points = [];
	        for (let i = 1; i <= locationCount; i++) {
	            points.push(CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, `x${i}`, `y${i}`));
	        }
	        const record = {
	            recordType: SchDocTypes_1.RecordType.SIGNAL_HARNESS,
	            index,
	            ownerIndex,
	            properties,
	            points,
	            color: CoordinateConverter_1.CoordinateConverter.convertColor(parseInt(properties['color']?.value ?? '0', 10)),
	            lineWidth: parseInt(properties['linewidth']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.SignalHarnessRecordSchema.parse(record);
	    }
	    static parseImplementationList(index, ownerIndex, properties) {
	        const record = {
	            recordType: SchDocTypes_1.RecordType.IMPLEMENTATION_LIST,
	            index,
	            ownerIndex,
	            properties,
	        };
	        return RecordTypes_1.ImplementationListRecordSchema.parse(record);
	    }
	    static parseImplementation(index, ownerIndex, properties) {
	        const record = {
	            recordType: SchDocTypes_1.RecordType.IMPLEMENTATION,
	            index,
	            ownerIndex,
	            properties,
	            modelName: properties['modelname']?.value ?? '',
	            modelType: properties['modeltype']?.value ?? '',
	            dataFile: properties['modeldatafile0']?.value ?? '',
	            description: properties['description']?.value ?? '',
	            isCurrent: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['iscurrent']?.value),
	        };
	        return RecordTypes_1.ImplementationRecordSchema.parse(record);
	    }
	    static parseImage(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.IMAGE,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            corner,
	            filename: properties['filename']?.value ?? '',
	            ownerPartId: parseInt(properties['ownerpartid']?.value ?? '-1', 10),
	            ownerPartDisplayMode: parseInt(properties['ownerpartdisplaymode']?.value ?? '0', 10),
	        };
	        return RecordTypes_1.ImageRecordSchema.parse(record);
	    }
	    static parseTemplate(index, ownerIndex, properties) {
	        const record = {
	            recordType: SchDocTypes_1.RecordType.TEMPLATE,
	            index,
	            ownerIndex,
	            properties,
	            filename: properties['filename']?.value ?? '',
	            isNotAccesible: CoordinateConverter_1.CoordinateConverter.altiumBool(properties['isnotaccesible']?.value),
	        };
	        return RecordTypes_1.TemplateRecordSchema.parse(record);
	    }
	    static parseCompileMask(index, ownerIndex, properties) {
	        const location = CoordinateConverter_1.CoordinateConverter.convertObjectLocation(properties);
	        const corner = CoordinateConverter_1.CoordinateConverter.parseFractionalPoint(properties, 'corner.x', 'corner.y');
	        const record = {
	            recordType: SchDocTypes_1.RecordType.COMPILE_MASK,
	            index,
	            ownerIndex,
	            properties,
	            location,
	            corner,
	        };
	        return RecordTypes_1.CompileMaskRecordSchema.parse(record);
	    }
	    static parseUnknown(recordType, index, ownerIndex, properties) {
	        return {
	            recordType,
	            index,
	            ownerIndex,
	            properties,
	        };
	    }
	}
	RecordParser.RecordParser = RecordParser$1;
	
	return RecordParser;
}

var AsciiStreamParser = {};

var hasRequiredAsciiStreamParser;

function requireAsciiStreamParser () {
	if (hasRequiredAsciiStreamParser) return AsciiStreamParser;
	hasRequiredAsciiStreamParser = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AsciiStreamParser = exports.ASCII_ICON_STORAGE_HEADER = void 0;
		const Util_1 = requireUtil();
		const PropertyParser_1 = requirePropertyParser();
		exports.ASCII_ICON_STORAGE_HEADER = '|HEADER=Icon storage';
		class AsciiStreamParser {
		    constructor(lines) {
		        this.lines = lines;
		    }
		    static async fromBlob(blob) {
		        const text = (0, Util_1.bytesToLatin1)(new Uint8Array(await blob.arrayBuffer()));
		        const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
		        return new AsciiStreamParser(lines);
		    }
		    parseStorage() {
		        return Promise.resolve(undefined);
		    }
		    parseFileHeader() {
		        const records = [];
		        for (const line of this.lines) {
		            if (line === exports.ASCII_ICON_STORAGE_HEADER) {
		                continue;
		            }
		            const properties = this.parseLine(line);
		            records.push(properties);
		        }
		        return Promise.resolve(records);
		    }
		    parseAdditional() {
		        throw new Error('ASCII schematic files do not support Additional streams');
		    }
		    parseLine(line) {
		        return PropertyParser_1.PropertyParser.parsePropertyPairs(line);
		    }
		}
		exports.AsciiStreamParser = AsciiStreamParser;
		
	} (AsciiStreamParser));
	return AsciiStreamParser;
}

var hasRequiredSchDocParser;

function requireSchDocParser () {
	if (hasRequiredSchDocParser) return SchDocParser;
	hasRequiredSchDocParser = 1;
	Object.defineProperty(SchDocParser, "__esModule", { value: true });
	SchDocParser.SchDocParser = void 0;
	const StreamParser_1 = requireStreamParser();
	const AltiumSchematic_1 = requireAltiumSchematic();
	const RecordParser_1 = requireRecordParser();
	const RecordTypes_1 = requireRecordTypes();
	const AsciiStreamParser_1 = requireAsciiStreamParser();
	const CFB_MAGIC = new Uint8Array([0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);
	class SchDocParser$1 {
	    constructor(streamParser, fileName) {
	        this.streamParser = streamParser;
	        this.fileName = fileName;
	    }
	    static async FromBlob(blob, fileName) {
	        if (await SchDocParser$1.IsCfb(blob)) {
	            const streamParser = await StreamParser_1.StreamParser.fromBlob(blob);
	            return new SchDocParser$1(streamParser, fileName);
	        }
	        else {
	            const streamParser = await AsciiStreamParser_1.AsciiStreamParser.fromBlob(blob);
	            return new SchDocParser$1(streamParser, fileName);
	        }
	    }
	    static async IsCfb(blob) {
	        const header = new Uint8Array(await blob.slice(0, CFB_MAGIC.length).arrayBuffer());
	        return header.length >= CFB_MAGIC.length && header.every((byte, i) => byte === CFB_MAGIC[i]);
	    }
	    async parse() {
	        const storageData = await this.streamParser.parseStorage();
	        const storage = this.buildStorageMap(storageData);
	        const fileHeaderRecords = await this.streamParser.parseFileHeader();
	        let additionalRecords;
	        try {
	            additionalRecords = await this.streamParser.parseAdditional();
	        }
	        catch {
	            additionalRecords = undefined;
	        }
	        const records = this.buildRecords(fileHeaderRecords, additionalRecords ?? []);
	        const version = this.extractVersion(records);
	        return new AltiumSchematic_1.AltiumSchematic(version, this.fileName, storage, records, storageData?.processingResults ?? []);
	    }
	    buildStorageMap(storageData) {
	        const storage = new Map();
	        if (storageData === undefined) {
	            return storage;
	        }
	        for (const file of storageData.files) {
	            storage.set(file.fileName, file.data);
	        }
	        return storage;
	    }
	    buildRecords(...propertyRecordGroups) {
	        const records = [];
	        const releasedProperties = {};
	        const strings = new Map();
	        const intern = (value) => {
	            const existing = strings.get(value);
	            if (existing !== undefined) {
	                return existing;
	            }
	            strings.set(value, value);
	            return value;
	        };
	        for (const propertyRecords of propertyRecordGroups) {
	            for (let index = 0; index < propertyRecords.length; index++) {
	                const properties = propertyRecords[index];
	                for (const property of Object.values(properties)) {
	                    if (property !== undefined) {
	                        property.key = intern(property.key);
	                        property.value = intern(property.value);
	                    }
	                }
	                records.push(this.createRecord(records.length, properties));
	                propertyRecords[index] = releasedProperties;
	            }
	        }
	        return records;
	    }
	    createRecord(index, properties) {
	        const recordTypeStr = properties.record?.value ?? '0';
	        const recordType = parseInt(recordTypeStr, 10);
	        const ownerIndexStr = properties.ownerindex?.value ?? '-1';
	        let ownerIndex = parseInt(ownerIndexStr, 10);
	        if (ownerIndex >= 0) {
	            ownerIndex += 1;
	        }
	        return RecordParser_1.RecordParser.parseRecord(recordType, index, ownerIndex, properties);
	    }
	    extractVersion(records) {
	        const headerRecord = records.find(RecordTypes_1.isHeaderRecord);
	        if (!headerRecord) {
	            throw new Error('HEADER record not found');
	        }
	        if (headerRecord.majorVersion && headerRecord.minorVersion) {
	            return `${headerRecord.majorVersion}.${headerRecord.minorVersion}`;
	        }
	        else if (headerRecord.majorVersion) {
	            return `${headerRecord.majorVersion}.x`;
	        }
	        return 'Unknown';
	    }
	}
	SchDocParser.SchDocParser = SchDocParser$1;
	
	return SchDocParser;
}

var hasRequiredAltiumValidation;

function requireAltiumValidation () {
	if (hasRequiredAltiumValidation) return altiumValidation;
	hasRequiredAltiumValidation = 1;
	Object.defineProperty(altiumValidation, "__esModule", { value: true });
	altiumValidation.AltiumProjectMetadataKey = void 0;
	altiumValidation.getAltiumFileType = getAltiumFileType;
	altiumValidation.validateAltiumFile = validateAltiumFile;
	altiumValidation.validateAltiumProjectStructureFile = validateAltiumProjectStructureFile;
	altiumValidation.detectAltiumProjects = detectAltiumProjects;
	const project_detection_1 = requireProjectDetection();
	const Util_1 = requireUtil();
	const altiumRecordFile_1 = requireAltiumRecordFile();
	const projectFileParser_1 = requireProjectFileParser();
	const PCAD_1 = requirePCAD();
	const altiumHarness_1 = requireAltiumHarness();
	const altiumAnnotation_1 = requireAltiumAnnotation();
	const SchDocParser_1 = requireSchDocParser();
	const AltiumSheetSymbol_1 = requireAltiumSheetSymbol();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	var AltiumProjectMetadataKey;
	(function (AltiumProjectMetadataKey) {
	    AltiumProjectMetadataKey["FileName"] = "file-name";
	    AltiumProjectMetadataKey["FileType"] = "file-type";
	    AltiumProjectMetadataKey["FileVersion"] = "file-version";
	    AltiumProjectMetadataKey["Project"] = "altium-project";
	    AltiumProjectMetadataKey["ProjectStructure"] = "project-structure";
	    AltiumProjectMetadataKey["NetlistAsSchematicModel"] = "netlist-schematic-model";
	    AltiumProjectMetadataKey["Harness"] = "harness";
	    AltiumProjectMetadataKey["Annotation"] = "annotation";
	    AltiumProjectMetadataKey["SchDoc"] = "SchDoc";
	})(AltiumProjectMetadataKey || (altiumValidation.AltiumProjectMetadataKey = AltiumProjectMetadataKey = {}));
	function isTopLevelDocument(record) {
	    return record.RECORD === 'TopLevelDocument';
	}
	function isSheetSymbol(record) {
	    return record.RECORD === 'SheetSymbol';
	}
	function isNoMainPathDocument(record) {
	    return record.RECORD === 'NoMainPathDocument';
	}
	function getAltiumFileType(filename) {
	    const path = filename.split('/');
	    if (path.length < 1) {
	        return false;
	    }
	    const parts = path[path.length - 1].split('.');
	    if (parts.length < 1) {
	        return false;
	    }
	    let ext = parts[parts.length - 1].toLowerCase();
	    if (parts.length >= 1) {
	        ext = '.' + ext;
	    }
	    switch (ext.toLowerCase().trim()) {
	        case altiumFileTypes_1.AltiumFileType.SchDoc.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.SchDoc;
	        case altiumFileTypes_1.AltiumFileType.Sch.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.Sch;
	        case altiumFileTypes_1.AltiumFileType.PcbDoc.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.PcbDoc;
	        case altiumFileTypes_1.AltiumFileType.Project.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.Project;
	        case altiumFileTypes_1.AltiumFileType.ProjectStructure.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.ProjectStructure;
	        case altiumFileTypes_1.AltiumFileType.ProjectVariants.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.ProjectVariants;
	        case altiumFileTypes_1.AltiumFileType.OutJob.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.OutJob;
	        case altiumFileTypes_1.AltiumFileType.PcbDrawing.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.PcbDrawing;
	        case altiumFileTypes_1.AltiumFileType.Harness.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.Harness;
	        case altiumFileTypes_1.AltiumFileType.BomDoc.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.BomDoc;
	        case altiumFileTypes_1.AltiumFileType.Stackup.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.Stackup;
	        case altiumFileTypes_1.AltiumFileType.Netlist.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.Netlist;
	        case altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase():
	            return altiumFileTypes_1.AltiumFileType.Annotation;
	    }
	    return false;
	}
	function altiumSemanticType(type) {
	    switch (type) {
	        case altiumFileTypes_1.AltiumFileType.Sch:
	        case altiumFileTypes_1.AltiumFileType.SchDoc:
	            return 'schematic';
	        case altiumFileTypes_1.AltiumFileType.PcbDoc:
	            return 'board';
	        case altiumFileTypes_1.AltiumFileType.Project:
	            return 'project';
	        case altiumFileTypes_1.AltiumFileType.BomDoc:
	            return 'bom';
	        case altiumFileTypes_1.AltiumFileType.Stackup:
	            return 'stackup';
	        case altiumFileTypes_1.AltiumFileType.Netlist:
	            return 'netlist';
	        default:
	            return 'other';
	    }
	}
	async function validateAltiumFile(filename, fileContents) {
	    const type = getAltiumFileType(filename);
	    if (!type) {
	        return {
	            isValid: false,
	            errors: [`Invalid Altium file or contents ${filename}`],
	        };
	    }
	    const semanticType = altiumSemanticType(type);
	    let validationResult;
	    try {
	        switch (type) {
	            case altiumFileTypes_1.AltiumFileType.Sch:
	            case altiumFileTypes_1.AltiumFileType.SchDoc:
	                validationResult = await validateAltiumSch(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.PcbDoc:
	                validationResult = await validateAltiumPcb(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.Project:
	                validationResult = await validateAltiumProjectFile(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.Harness:
	                validationResult = await (0, altiumHarness_1.validateAltiumHarnessFile)(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.BomDoc:
	                validationResult = await validateAltiumBomFile(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.ProjectStructure:
	                validationResult = await validateAltiumProjectStructureFile(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.Stackup:
	                validationResult = await validateAltiumStackupFile(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.Netlist:
	                validationResult = await validateAltiumNetlistFile(filename, fileContents);
	                break;
	            case altiumFileTypes_1.AltiumFileType.Annotation:
	                validationResult = await (0, altiumAnnotation_1.validateAltiumAnnotationFile)(filename, fileContents);
	                break;
	            default:
	                return {
	                    isValid: true,
	                    data: {
	                        semanticType,
	                        fileFormat: {
	                            cadTool: 'altium',
	                            type: type,
	                        },
	                        referencedFiles: [],
	                        metadata: {},
	                    },
	                };
	        }
	    }
	    catch (e) {
	        return {
	            isValid: false,
	            semanticType,
	            errors: [`Invalid Altium contents ${filename}`, e instanceof Error ? e.message : String(e)],
	        };
	    }
	    if (!validationResult) {
	        return {
	            isValid: false,
	            semanticType,
	            errors: [`Invalid Altium contents ${filename}`],
	        };
	    }
	    return {
	        isValid: true,
	        data: {
	            semanticType: semanticType,
	            fileFormat: {
	                cadTool: 'altium',
	                type: type,
	                version: validationResult.version,
	            },
	            referencedFiles: validationResult.referencedFiles,
	            metadata: validationResult.metadata,
	        },
	    };
	}
	async function validateAltiumBinaryPreamble(fileContents) {
	    const expected = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1, 0, 0, 0, 0, 0, 0, 0, 0];
	    const arrBuff = new Uint8Array(await fileContents.slice(0, expected.length).arrayBuffer());
	    for (let i = 0; i < expected.length; i++) {
	        if (arrBuff[i] !== expected[i]) {
	            return false;
	        }
	    }
	    return true;
	}
	async function validateAltiumSch(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.schdoc')) {
	        return undefined;
	    }
	    const parser = await SchDocParser_1.SchDocParser.FromBlob(fileContents, filename);
	    const schematic = await parser.parse();
	    const referencedFiles = new Set(schematic.getSheetSymbols().map((s) => (0, AltiumSheetSymbol_1.withDefaultSchDocExtension)(s.getFileNameText())));
	    return {
	        metadata: {
	            [AltiumProjectMetadataKey.FileType]: 'Altium SchDoc Schematic',
	            [AltiumProjectMetadataKey.FileVersion]: schematic.version,
	            [AltiumProjectMetadataKey.SchDoc]: schematic,
	        },
	        referencedFiles: [...referencedFiles],
	        version: schematic.version,
	    };
	}
	async function validateAltiumPcb(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.pcbdoc')) {
	        return undefined;
	    }
	    if (!(await validateAltiumBinaryPreamble(fileContents))) {
	        return undefined;
	    }
	    const textContents = await (0, Util_1.getBlobTextCleanPartial)(fileContents, 65536);
	    const versionMatch = /PCB\s*(\d+\.\d+)\sBinary\sFile/.exec(textContents);
	    if (!versionMatch) {
	        return undefined;
	    }
	    return {
	        metadata: {
	            [AltiumProjectMetadataKey.FileType]: 'Altium PcbDoc',
	            [AltiumProjectMetadataKey.FileVersion]: versionMatch[1],
	        },
	        referencedFiles: [],
	        version: versionMatch[1],
	    };
	}
	async function validateAltiumProjectFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.prjpcb')) {
	        return undefined;
	    }
	    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
	    const parsed = (0, projectFileParser_1.parseAltiumProjectFile)(filename.substring(0, Math.max(filename.lastIndexOf('/'), filename.length)), textContents);
	    if (!parsed?.Design?.Version) {
	        return undefined;
	    }
	    const referencedFiles = [];
	    for (const document of parsed.Document) {
	        if (document.DocumentPath) {
	            referencedFiles.push(document.DocumentPath.replace(/\\/g, '/'));
	        }
	    }
	    return {
	        metadata: {
	            [AltiumProjectMetadataKey.FileType]: altiumFileTypes_1.AltiumFileType.Project,
	            [AltiumProjectMetadataKey.FileVersion]: parsed.Design.Version,
	            [AltiumProjectMetadataKey.Project]: parsed,
	        },
	        referencedFiles,
	        version: parsed.Design.Version,
	    };
	}
	async function validateAltiumBomFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.bomdoc')) {
	        return undefined;
	    }
	    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
	    const parsed = (0, altiumRecordFile_1.parseAltiumRecordFile)(textContents, undefined, true);
	    if (!parsed || parsed.length === 0) {
	        return undefined;
	    }
	    if (!parsed.some((record) => record.RECORD === 'BOM')) {
	        return undefined;
	    }
	    return {
	        referencedFiles: [],
	        metadata: {
	            [AltiumProjectMetadataKey.FileType]: 'Altium BOM',
	        },
	    };
	}
	async function validateAltiumProjectStructureFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.prjpcbstructure')) {
	        return undefined;
	    }
	    const referencedFiles = new Set();
	    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
	    const parsed = (0, altiumRecordFile_1.parseAltiumRecordFile)(textContents, undefined, true);
	    if (!parsed || parsed.length === 0) {
	        return undefined;
	    }
	    let foundTld = false;
	    const topLevelDocuments = [];
	    const sheetSymbols = [];
	    const noMainPathDocuments = [];
	    const otherRecords = [];
	    for (const record of parsed) {
	        if (isTopLevelDocument(record)) {
	            foundTld = true;
	            topLevelDocuments.push(record);
	            if (record.FILENAME) {
	                referencedFiles.add(record.FILENAME);
	            }
	        }
	        else if (isSheetSymbol(record)) {
	            sheetSymbols.push(record);
	            if (record.FILENAME) {
	                referencedFiles.add(record.FILENAME);
	            }
	            if (record.SOURCEDOCUMENT) {
	                referencedFiles.add(record.SOURCEDOCUMENT);
	            }
	        }
	        else if (isNoMainPathDocument(record)) {
	            noMainPathDocuments.push(record);
	            if (record.FILENAME) {
	                referencedFiles.add(record.FILENAME);
	            }
	            if (record.SOURCEDOCUMENT) {
	                referencedFiles.add(record.SOURCEDOCUMENT);
	            }
	        }
	        else {
	            otherRecords.push(record);
	        }
	    }
	    if (!foundTld) {
	        return undefined;
	    }
	    const statistics = {
	        topLevelDocumentCount: topLevelDocuments.length,
	        sheetSymbolCount: sheetSymbols.length,
	        noMainPathDocumentCount: noMainPathDocuments.length,
	        otherRecordCount: otherRecords.length,
	        totalReferencedFiles: referencedFiles.size,
	    };
	    const projectStructureData = {
	        [AltiumProjectMetadataKey.FileName]: filename,
	        [AltiumProjectMetadataKey.FileType]: altiumFileTypes_1.AltiumFileType.ProjectStructure,
	        totalRecords: parsed.length,
	        topLevelDocuments,
	        sheetSymbols,
	        noMainPathDocuments,
	        statistics,
	    };
	    if (otherRecords.length > 0) {
	        projectStructureData.otherRecords = otherRecords;
	    }
	    const metadata = {
	        [AltiumProjectMetadataKey.FileType]: altiumFileTypes_1.AltiumFileType.ProjectStructure,
	        [AltiumProjectMetadataKey.ProjectStructure]: projectStructureData,
	    };
	    return {
	        metadata,
	        referencedFiles: Array.from(referencedFiles),
	    };
	}
	async function validateAltiumStackupFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.stackup')) {
	        return undefined;
	    }
	    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
	    const parsed = (0, altiumRecordFile_1.parseAltiumRecordFile)(textContents, undefined, false, altiumRecordFile_1.AltiumRecordStackupType);
	    if (!parsed || parsed.length === 0) {
	        return undefined;
	    }
	    const stackupRecord = parsed.find((record) => record.RECORD === 'STACKUP');
	    if (!stackupRecord) {
	        return undefined;
	    }
	    const version = stackupRecord.STACKUPVERSION.toString();
	    return {
	        referencedFiles: [],
	        metadata: {
	            [AltiumProjectMetadataKey.FileType]: 'Altium Stackup',
	        },
	        version,
	    };
	}
	async function validateAltiumNetlistFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith('.net')) {
	        return undefined;
	    }
	    const textContents = await (0, Util_1.getBlobTextCleanAnsi)(fileContents);
	    const result = (0, PCAD_1.validatePcadFile)(filename, textContents);
	    if (!result.isValid) {
	        return undefined;
	    }
	    try {
	        const parsed = (0, PCAD_1.parsePcadNetlist)(textContents);
	        return {
	            referencedFiles: [],
	            metadata: {
	                [AltiumProjectMetadataKey.FileType]: 'Altium Netlist',
	                [AltiumProjectMetadataKey.NetlistAsSchematicModel]: parsed.schematic,
	            },
	            version: '1.0',
	            warnings: parsed.warnings,
	        };
	    }
	    catch (e) {
	        return undefined;
	    }
	}
	function detectAltiumProjects(files, fileDetails) {
	    return (0, project_detection_1.detectAnchoredProjects)({
	        tool: 'altium',
	        toolDisplayName: 'Altium',
	        files,
	        fileDetails,
	        primaryAnchorMatch: (0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.Project),
	        primaryAnchorLabel: altiumFileTypes_1.AltiumFileType.Project,
	        secondaryAnchorMatch: (0, project_detection_1.endsWithAnyExt)([altiumFileTypes_1.AltiumFileType.ProjectStructure, altiumFileTypes_1.AltiumFileType.ProjectVariants]),
	        secondaryAnchorLabel: `${altiumFileTypes_1.AltiumFileType.ProjectStructure} or ${altiumFileTypes_1.AltiumFileType.ProjectVariants}`,
	    });
	}
	
	return altiumValidation;
}

var hasRequiredAltiumProjectFileUtil;

function requireAltiumProjectFileUtil () {
	if (hasRequiredAltiumProjectFileUtil) return altiumProjectFileUtil;
	hasRequiredAltiumProjectFileUtil = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AltiumProjectFileUtil = exports.AltiumProjectFileDesignDefaults = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const altiumFileTypes_1 = requireAltiumFileTypes();
		const altiumValidation_1 = requireAltiumValidation();
		const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
		exports.AltiumProjectFileDesignDefaults = Object.freeze({
		    Design: Object.freeze({
		        ChannelDesignatorFormatString: '$Component_$RoomName',
		        ChannelRoomLevelSeperator: '_',
		        AllowPortNetNames: false,
		        AllowSheetEntryNetNames: false,
		        AppendSheetNumberToLocalNets: false,
		        AutoSheetNumbering: false,
		        PowerPortNamesTakePriority: altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.No,
		        NameNetsHierarchically: altiumProjectFileTypes_1.NameNetsHierarchicallyMode.No,
		        HierarchyMode: altiumProjectFileTypes_1.HierarchyMode.Automatic,
		        ChannelRoomNamingStyle: 0,
		        AnnotateSortOrder: altiumProjectFileTypes_1.AnnotateSortOrder.AcrossThenUp,
		        AnnotateProcessLocation: altiumProjectFileTypes_1.AnnotateProcessLocation.Designator,
		    }),
		});
		const EMPTY_STRING_MAP = new Map();
		class AltiumProjectFileUtil {
		    static { this.UNAPPLIED_VARIANT_COLUMN_PREFIX = 'Variant: '; }
		    static { this.NOT_FITTED_LABEL = 'Not Fitted'; }
		    static { this.VARIATION_KIND_LABELS = {
		        [altiumProjectFileTypes_1.AltiumVariationKind.Fitted]: 'Fitted',
		        [altiumProjectFileTypes_1.AltiumVariationKind.NotFitted]: AltiumProjectFileUtil.NOT_FITTED_LABEL,
		        [altiumProjectFileTypes_1.AltiumVariationKind.AlternatePart]: 'Alternate Part',
		    }; }
		    constructor(projectFile, prjpcbStructureFile) {
		        this.projectFile = projectFile;
		        this.prjpcbStructureFile = prjpcbStructureFile;
		        this.paramVariationIndexes = new Map();
		        this.projectDocumentPathResolutions = new Map();
		    }
		    get projectFileName() {
		        return this.projectFile[altiumValidation_1.AltiumProjectMetadataKey.FileName];
		    }
		    getDifferentialPairSuffixes() {
		        const declaredSuffixes = this.projectFile.DiffPairSuffix?.flatMap((suffix) => [suffix.Positive, suffix.Negative]) ?? [];
		        const suffixes = ['_P', '_N', ...declaredSuffixes].filter((suffix) => suffix.length > 0 && suffix.startsWith('_'));
		        const uniqueSuffixes = new Map();
		        for (const suffix of suffixes) {
		            const key = suffix.toLowerCase();
		            if (!uniqueSuffixes.has(key)) {
		                uniqueSuffixes.set(key, suffix);
		            }
		        }
		        return Array.from(uniqueSuffixes.values()).sort((a, b) => b.length - a.length);
		    }
		    get sheetCount() {
		        return this.projectFile.Document?.filter((d) => d.DocumentPath.toLowerCase().endsWith('.schdoc')).length ?? 0;
		    }
		    get hasPrjpcbStructureFile() {
		        return this.prjpcbStructureFile !== undefined;
		    }
		    get listsNoSchematics() {
		        return !this.hasPrjpcbStructureFile && this.sheetCount === 0;
		    }
		    checkHierarchyModeRequiresProjectStructure(hierarchyMode) {
		        return hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical;
		    }
		    getDesignConfiguration(name) {
		        if (!this.projectFile?.Design || !(name in this.projectFile.Design)) {
		            return exports.AltiumProjectFileDesignDefaults.Design[name];
		        }
		        return this.projectFile?.Design?.[name];
		    }
		    getTopLevelDocuments(type) {
		        const filterType = (f) => (type ? f.toLowerCase().endsWith(type.toLowerCase()) : true);
		        const path = this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName] ?? this.projectFileName;
		        const isUsingPrjStructure = path === this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName];
		        if (this.prjpcbStructureFile?.topLevelDocuments) {
		            return this.makeFilesRelative(this.prjpcbStructureFile.topLevelDocuments.filter((d) => filterType(d.FILENAME)).map((d) => d.FILENAME), path, isUsingPrjStructure);
		        }
		        if (this.projectFile.Document?.length) {
		            const docs = this.projectFile.Document?.sort((a, b) => (a.AnnotateOrder ?? 0) - (b.AnnotateOrder ?? 0))
		                .filter((d) => filterType(d.DocumentPath))
		                .map((d) => d.DocumentPath);
		            return this.makeFilesRelative(docs, path, isUsingPrjStructure);
		        }
		        if (this.prjpcbStructureFile?.sheetSymbols.length === 1) {
		            return this.makeFilesRelative([this.prjpcbStructureFile.sheetSymbols[0].FILENAME], path, isUsingPrjStructure).filter(filterType);
		        }
		        throw new Error('No available settings to read top level document');
		    }
		    getNoMainPathDocuments(type) {
		        const filterType = (f) => (type ? f.toLowerCase().endsWith(type.toLowerCase()) : true);
		        const path = this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName] ?? this.projectFileName;
		        const isUsingPrjStructure = path === this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName];
		        const noMainPathDocuments = this.prjpcbStructureFile?.noMainPathDocuments ?? [];
		        return this.makeFilesRelative(noMainPathDocuments.filter((d) => filterType(d.FILENAME)).map((d) => d.FILENAME), path, isUsingPrjStructure);
		    }
		    getFlatProjectDocuments(type) {
		        const seen = new Set();
		        const documents = [...this.getTopLevelDocuments(type), ...this.getNoMainPathDocuments(type)];
		        return documents.filter((document) => {
		            const normalized = document.replace(/\\/g, '/').toLowerCase();
		            if (seen.has(normalized)) {
		                return false;
		            }
		            seen.add(normalized);
		            return true;
		        });
		    }
		    getPartVariationForUniqueId(uniqueId, variant = this.projectFile.Design?.CurrentVariant) {
		        const projectVariant = this.projectFile.ProjectVariant?.find((v) => v.Description === variant);
		        if (!projectVariant) {
		            if (!variant) {
		                return undefined;
		            }
		            throw new Error(`Could not find project variant '${variant}'`);
		        }
		        return projectVariant.Variations.find((v) => v.UniqueId === uniqueId);
		    }
		    getUnappliedVariantColumnsForUniqueId(uniqueId) {
		        if (this.projectFile.Design?.CurrentVariant) {
		            return EMPTY_STRING_MAP;
		        }
		        this.unappliedVariantColumns ??= this.buildUnappliedVariantColumns();
		        return this.unappliedVariantColumns.get(uniqueId) ?? EMPTY_STRING_MAP;
		    }
		    getParamVariationsForVariation(variation, variant = this.projectFile.Design?.CurrentVariant) {
		        if (variant === undefined) {
		            return EMPTY_STRING_MAP;
		        }
		        let index = this.paramVariationIndexes.get(variant);
		        if (!index) {
		            index = this.buildParamVariationIndex(variant);
		            this.paramVariationIndexes.set(variant, index);
		        }
		        return index.get(variation.Designator) ?? EMPTY_STRING_MAP;
		    }
		    buildUnappliedVariantColumns() {
		        const index = new Map();
		        for (const projectVariant of this.projectFile.ProjectVariant ?? []) {
		            const column = `${AltiumProjectFileUtil.UNAPPLIED_VARIANT_COLUMN_PREFIX}${projectVariant.Description}`;
		            for (const variation of projectVariant.Variations) {
		                let columns = index.get(variation.UniqueId);
		                if (!columns) {
		                    columns = new Map();
		                    index.set(variation.UniqueId, columns);
		                }
		                columns.set(column, AltiumProjectFileUtil.VARIATION_KIND_LABELS[variation.Kind] ?? `Kind ${variation.Kind}`);
		            }
		        }
		        return index;
		    }
		    buildParamVariationIndex(variant) {
		        const projectVariant = this.projectFile.ProjectVariant?.find((v) => v.Description === variant);
		        const index = new Map();
		        for (const paramVariation of projectVariant?.ParamVariations ?? []) {
		            if (!paramVariation.ParameterName) {
		                continue;
		            }
		            let overrides = index.get(paramVariation.Designator);
		            if (!overrides) {
		                overrides = new Map();
		                index.set(paramVariation.Designator, overrides);
		            }
		            overrides.set(paramVariation.ParameterName, paramVariation.VariantValue);
		        }
		        return index;
		    }
		    makeFilesRelative(files, path, searchFilesInProjectFile) {
		        if (searchFilesInProjectFile) {
		            return files.map((fileName) => {
		                const resolvedPath = this.resolveProjectDocumentPath(fileName).documentPath;
		                if (resolvedPath !== undefined) {
		                    return resolvedPath.replace(/\\/g, '/');
		                }
		                const normalizedFileName = AltiumProjectFileUtil.normalizeDocumentPath(fileName);
		                const matchingDocuments = AltiumProjectFileUtil.uniqueNormalizedDocuments((this.projectFile.Document ?? []).filter((document) => {
		                    const documentPath = AltiumProjectFileUtil.normalizeDocumentPath(document.DocumentPath);
		                    return documentPath === normalizedFileName || documentPath.endsWith(`/${normalizedFileName}`);
		                }));
		                return (matchingDocuments.length === 1 ? matchingDocuments[0].DocumentPath : fileName).replace(/\\/g, '/');
		            });
		        }
		        if (path === undefined) {
		            return files;
		        }
		        const resolvedPath = sigma_engine_shared_1.Files.dirnamePath(sigma_engine_shared_1.Files.resolvePath(path));
		        return files.map((f) => sigma_engine_shared_1.Files.relativePath(resolvedPath, sigma_engine_shared_1.Files.resolvePath(f.replace(/\\/g, '/'))));
		    }
		    getDesignConfigurationBoolean(name) {
		        return this.getDesignConfiguration(name) == 1;
		    }
		    getAltiumDocumentConfiguration(documentPath) {
		        const resolution = this.resolveProjectDocumentPath(documentPath);
		        return this.projectFile?.Document?.find((document) => document.DocumentPath === resolution.documentPath);
		    }
		    resolveProjectDocumentPath(fileKey) {
		        const normalizedFileKey = AltiumProjectFileUtil.normalizeDocumentPath(fileKey);
		        const cached = this.projectDocumentPathResolutions.get(normalizedFileKey);
		        if (cached) {
		            return cached;
		        }
		        const keyIsSpecial = AltiumProjectFileUtil.isSpecialDocumentPath(fileKey, normalizedFileKey);
		        const documents = this.projectFile.Document ?? [];
		        const exactMatches = AltiumProjectFileUtil.uniqueNormalizedDocuments(documents.filter((document) => AltiumProjectFileUtil.normalizeDocumentPath(document.DocumentPath) === normalizedFileKey));
		        const suffixMatches = exactMatches.length > 0 || keyIsSpecial ?
		            []
		            : documents.filter((document) => {
		                const listedPath = AltiumProjectFileUtil.normalizeDocumentPath(document.DocumentPath);
		                return (!AltiumProjectFileUtil.isSpecialDocumentPath(document.DocumentPath, listedPath) &&
		                    normalizedFileKey.endsWith(`/${listedPath}`));
		            });
		        const matches = exactMatches.length > 0 ?
		            exactMatches
		            : AltiumProjectFileUtil.mostSpecificPaths(AltiumProjectFileUtil.uniqueNormalizedDocuments(suffixMatches));
		        const document = matches[0];
		        if (!document) {
		            const resolution = Object.freeze({});
		            this.projectDocumentPathResolutions.set(normalizedFileKey, resolution);
		            return resolution;
		        }
		        const resolution = Object.freeze({ documentPath: document.DocumentPath });
		        this.projectDocumentPathResolutions.set(normalizedFileKey, resolution);
		        return resolution;
		    }
		    getAnnotationDocumentPaths() {
		        return this.getAnnotationDocumentSelection().paths;
		    }
		    getAnnotationDocumentPathDiagnostics() {
		        return this.getAnnotationDocumentSelection().diagnostics;
		    }
		    getAnnotationDocumentSelection() {
		        const annotationDocuments = (this.projectFile.Document ?? []).filter((document) => document.DocumentPath.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase()));
		        const diagnostics = [];
		        const documentsByIndex = new Map();
		        for (const document of annotationDocuments) {
		            if (!Number.isSafeInteger(document._DocumentIndex) || document._DocumentIndex < 0) {
		                diagnostics.push(`annotation-manager-index-ambiguous: ${document.DocumentPath} has malformed _DocumentIndex ${String(document._DocumentIndex)}`);
		                continue;
		            }
		            const indexedDocuments = documentsByIndex.get(document._DocumentIndex) ?? [];
		            indexedDocuments.push(document);
		            documentsByIndex.set(document._DocumentIndex, indexedDocuments);
		        }
		        for (const [index, documents] of documentsByIndex) {
		            if (documents.length > 1) {
		                diagnostics.push(`annotation-manager-index-ambiguous: _DocumentIndex ${index} is shared by ${documents.map((document) => document.DocumentPath).join(', ')}`);
		            }
		        }
		        if (diagnostics.length > 0) {
		            return { paths: [], diagnostics };
		        }
		        return {
		            paths: AltiumProjectFileUtil.uniqueNormalizedDocuments([...annotationDocuments].sort((left, right) => left._DocumentIndex - right._DocumentIndex)).map((document) => document.DocumentPath),
		            diagnostics: [],
		        };
		    }
		    getDocumentUniqueId(documentPath) {
		        return this.getAltiumDocumentConfiguration(documentPath)?.DocumentUniqueId;
		    }
		    getAltiumSchematicAnnotationOrder(fileName) {
		        const order = this.getAltiumDocumentConfiguration(fileName)?.AnnotateOrder;
		        if (order === undefined) {
		            throw new Error(`Could not find annotation order for ${fileName}`);
		        }
		        if (typeof order === 'string') {
		            return parseInt(order, 10);
		        }
		        return order;
		    }
		    getProjectParameter(key) {
		        const normalizedKey = key.toLowerCase();
		        return this.projectFile?.Parameter?.find((p) => typeof p.Name === 'string' && p.Name.toLowerCase() === normalizedKey)?.Value;
		    }
		    getAltiumSchematicSheetNumber(fileName) {
		        const resolution = this.resolveProjectDocumentPath(fileName);
		        const projectDocumentPath = resolution.documentPath ?? fileName;
		        const sheetNumber = (this.prjpcbStructureFile?.topLevelDocuments.find((d) => this.matchesStructureFileName(d.FILENAME, projectDocumentPath)) ??
		            this.prjpcbStructureFile?.sheetSymbols.find((d) => this.matchesStructureFileName(d.FILENAME, projectDocumentPath)) ??
		            this.prjpcbStructureFile?.noMainPathDocuments.find((d) => this.matchesStructureFileName(d.FILENAME, projectDocumentPath)))?.SHEETNUMBER;
		        if (sheetNumber === undefined) {
		            throw new Error(`Could not find sheet number for ${fileName}`);
		        }
		        if (typeof sheetNumber === 'string') {
		            return parseInt(sheetNumber, 10);
		        }
		        return sheetNumber;
		    }
		    getUnambiguousAltiumSchematicSheetNumber(fileName) {
		        const resolution = this.resolveProjectDocumentPath(fileName);
		        const projectDocumentPath = resolution.documentPath ?? fileName;
		        const records = [
		            ...(this.prjpcbStructureFile?.topLevelDocuments ?? []),
		            ...(this.prjpcbStructureFile?.sheetSymbols ?? []),
		            ...(this.prjpcbStructureFile?.noMainPathDocuments ?? []),
		        ].filter((record) => this.matchesStructureFileName(record.FILENAME, projectDocumentPath));
		        if (records.length === 0) {
		            throw new Error(`Could not find sheet number for ${fileName}`);
		        }
		        if (records.length > 1) {
		            throw new Error(`Multiple structural sheet records match ${fileName}`);
		        }
		        const sheetNumber = records[0]?.SHEETNUMBER;
		        if (sheetNumber === undefined) {
		            throw new Error(`Could not find sheet number for ${fileName}`);
		        }
		        const parsedSheetNumber = Number(sheetNumber);
		        if (!Number.isInteger(parsedSheetNumber) || parsedSheetNumber < 1) {
		            throw new Error(`Invalid sheet number for ${fileName}`);
		        }
		        return parsedSheetNumber;
		    }
		    matchesStructureFileName(structureFileName, fileName) {
		        const normalizedStructureFileName = AltiumProjectFileUtil.normalizeDocumentPath(structureFileName);
		        const normalizedFileName = AltiumProjectFileUtil.normalizeDocumentPath(fileName);
		        if (normalizedStructureFileName === normalizedFileName) {
		            return true;
		        }
		        return this.resolveProjectDocumentPath(structureFileName).documentPath === fileName;
		    }
		    static normalizeDocumentPath(path) {
		        return path
		            .replace(/\\/g, '/')
		            .replace(/(?<!^)\/{2,}/g, '/')
		            .replace(/^(\.\/)+/, '');
		    }
		    static isAbsoluteDocumentPath(path) {
		        const slashPath = path.replace(/\\/g, '/');
		        return slashPath.startsWith('/') || /^[A-Za-z]:\//.test(slashPath);
		    }
		    static hasParentSegment(path) {
		        return path.split('/').includes('..');
		    }
		    static isSpecialDocumentPath(rawPath, normalizedPath) {
		        return this.isAbsoluteDocumentPath(rawPath) || this.hasParentSegment(normalizedPath);
		    }
		    static uniqueNormalizedDocuments(documents) {
		        const uniqueDocuments = new Map();
		        for (const document of documents) {
		            const normalizedPath = this.normalizeDocumentPath(document.DocumentPath);
		            const existing = uniqueDocuments.get(normalizedPath);
		            if (existing === undefined ||
		                (existing.DocumentPath !== normalizedPath && document.DocumentPath === normalizedPath)) {
		                uniqueDocuments.set(normalizedPath, document);
		            }
		        }
		        return Array.from(uniqueDocuments.values());
		    }
		    static mostSpecificPaths(documents) {
		        const maximumSegments = Math.max(0, ...documents.map((document) => this.normalizeDocumentPath(document.DocumentPath).split('/').length));
		        return documents.filter((document) => this.normalizeDocumentPath(document.DocumentPath).split('/').length === maximumSegments);
		    }
		}
		exports.AltiumProjectFileUtil = AltiumProjectFileUtil;
		
	} (altiumProjectFileUtil));
	return altiumProjectFileUtil;
}

var hasRequiredDnpClassificationGate;

function requireDnpClassificationGate () {
	if (hasRequiredDnpClassificationGate) return dnpClassificationGate;
	hasRequiredDnpClassificationGate = 1;
	Object.defineProperty(dnpClassificationGate, "__esModule", { value: true });
	dnpClassificationGate.DnpClassificationGate = dnpClassificationGate.DnpClassificationOutcome = void 0;
	const altiumProjectFileUtil_1 = requireAltiumProjectFileUtil();
	var DnpClassificationOutcome;
	(function (DnpClassificationOutcome) {
	    DnpClassificationOutcome["SkippedParserFlagPresent"] = "skipped_parser_flag_present";
	    DnpClassificationOutcome["SkippedCadTool"] = "skipped_cad_tool";
	    DnpClassificationOutcome["SkippedNoModules"] = "skipped_no_modules";
	    DnpClassificationOutcome["NoCandidates"] = "no_candidates";
	    DnpClassificationOutcome["Classified"] = "classified";
	})(DnpClassificationOutcome || (dnpClassificationGate.DnpClassificationOutcome = DnpClassificationOutcome = {}));
	class DnpClassificationGate {
	    static { this.CAD_TOOLS = new Set([
	        'orcad',
	        'eagle',
	    ]); }
	    static { this.CLASSIFY_UNAPPLIED_ALTIUM_VARIANTS = false; }
	    static skipReason(cadTool, modules, cadTools = DnpClassificationGate.CAD_TOOLS, classifyUnappliedAltiumVariants = DnpClassificationGate.CLASSIFY_UNAPPLIED_ALTIUM_VARIANTS) {
	        if (modules.length === 0) {
	            return DnpClassificationOutcome.SkippedNoModules;
	        }
	        if (modules.some((module) => module.isDnp === true)) {
	            return DnpClassificationOutcome.SkippedParserFlagPresent;
	        }
	        if (!cadTools.has(cadTool) &&
	            !(classifyUnappliedAltiumVariants && DnpClassificationGate.hasUnappliedNotFittedVariant(cadTool, modules))) {
	            return DnpClassificationOutcome.SkippedCadTool;
	        }
	        return null;
	    }
	    static hasUnappliedNotFittedVariant(cadTool, modules) {
	        return (cadTool === 'altium' &&
	            modules.some((module) => Object.entries(module.metadata ?? {}).some(([key, value]) => key.startsWith(altiumProjectFileUtil_1.AltiumProjectFileUtil.UNAPPLIED_VARIANT_COLUMN_PREFIX) &&
	                value === altiumProjectFileUtil_1.AltiumProjectFileUtil.NOT_FITTED_LABEL)));
	    }
	}
	dnpClassificationGate.DnpClassificationGate = DnpClassificationGate;
	
	return dnpClassificationGate;
}

var dnpColumnCandidateFinder = {};

var dnpMetadataValue = {};

var hasRequiredDnpMetadataValue;

function requireDnpMetadataValue () {
	if (hasRequiredDnpMetadataValue) return dnpMetadataValue;
	hasRequiredDnpMetadataValue = 1;
	Object.defineProperty(dnpMetadataValue, "__esModule", { value: true });
	dnpMetadataValue.DnpMetadataValue = void 0;
	class DnpMetadataValue {
	    static { this.UNAMBIGUOUS_MARKERS = [
	        'dnp',
	        'dni',
	        'dnf',
	        'dns',
	        'nopop',
	        'no_stuff',
	        'no-stuff',
	        'nostuff',
	        'no stuff',
	        'not fitted',
	        'not_fitted',
	        'nofit',
	        'not placed',
	        'not_placed',
	        'do not populate',
	        'do_not_populate',
	        'not populated',
	        'do not place',
	        'do_not_place',
	    ]; }
	    static { this.AMBIGUOUS_MARKERS = ['nc', 'nm', 'ni']; }
	    static { this.MARKER_PATTERNS = DnpMetadataValue.buildMarkerPatterns(); }
	    static normalize(raw) {
	        if (raw === undefined || raw === null || typeof raw === 'object') {
	            return null;
	        }
	        const text = String(raw).trim();
	        return text.length > 0 ? text.toLowerCase() : null;
	    }
	    static isMarker(raw) {
	        const normalized = DnpMetadataValue.normalize(raw);
	        if (normalized === null) {
	            return false;
	        }
	        return DnpMetadataValue.MARKER_PATTERNS.some((pattern) => pattern.test(normalized));
	    }
	    static buildMarkerPatterns() {
	        const alternation = (markers) => [...markers]
	            .sort((a, b) => b.length - a.length)
	            .map((marker) => marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '\\s+'))
	            .join('|');
	        const any = alternation([...DnpMetadataValue.UNAMBIGUOUS_MARKERS, ...DnpMetadataValue.AMBIGUOUS_MARKERS]);
	        const unambiguous = alternation(DnpMetadataValue.UNAMBIGUOUS_MARKERS);
	        const ambiguous = alternation(DnpMetadataValue.AMBIGUOUS_MARKERS);
	        const open = '[(<\\[]?';
	        const close = '[)>\\]]?';
	        return [
	            new RegExp(`^${open}(?:${any})${close}$`),
	            new RegExp(`^${open}(?:${unambiguous})${close}(?=[\\s_/:(])`),
	            new RegExp(`^${open}(?:${ambiguous})${close}(?=[_/:(]|\\s+\\()`),
	            new RegExp(`(?:^|\\s)[(<\\[](?:${any})[)>\\]](?=\\s|$)`),
	            new RegExp(`(?:^|\\s)(?:${unambiguous})(?=\\s|$)`),
	        ];
	    }
	}
	dnpMetadataValue.DnpMetadataValue = DnpMetadataValue;
	
	return dnpMetadataValue;
}

var hasRequiredDnpColumnCandidateFinder;

function requireDnpColumnCandidateFinder () {
	if (hasRequiredDnpColumnCandidateFinder) return dnpColumnCandidateFinder;
	hasRequiredDnpColumnCandidateFinder = 1;
	Object.defineProperty(dnpColumnCandidateFinder, "__esModule", { value: true });
	dnpColumnCandidateFinder.DnpColumnCandidateFinder = dnpColumnCandidateFinder.dnpColumnStateSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const dnp_metadata_value_1 = requireDnpMetadataValue();
	dnpColumnCandidateFinder.dnpColumnStateSchema = zod_1.z.object({
	    value: zod_1.z.string(),
	    count: zod_1.z.number().int().nonnegative(),
	});
	class DnpColumnCandidateFinder {
	    static { this.MARKER_EXAMPLE_LIMIT = 5; }
	    static { this.PARSER_KEY_PREFIXES = [
	        'orcad.',
	        'altium.',
	        'kicad.',
	        'allegro.',
	        'eagle.',
	    ]; }
	    static { this.STRONG_NAME_PATTERN = /dnp|dni|dnf|dns|nopop|populat|fitted|nofit|stuff|install|placed|placement/; }
	    static { this.WEAK_NAME_PATTERN = /bom|exclude|ignore|assembly|variant|fit/; }
	    static { this.FLAG_VALUES = new Set([
	        '@',
	        'x',
	        '*',
	        '-',
	        '+',
	        'y',
	        'n',
	        'yes',
	        'no',
	        'true',
	        'false',
	        '0',
	        '1',
	    ]); }
	    static find(metadataRows) {
	        const valueCounts = DnpColumnCandidateFinder.countValues(metadataRows);
	        const flagColumns = [];
	        const markerColumns = [];
	        for (const [column, counts] of valueCounts) {
	            const presentCount = [...counts.values()].reduce((sum, count) => sum + count, 0);
	            const absentCount = metadataRows.length - presentCount;
	            const stateCount = counts.size + (absentCount > 0 ? 1 : 0);
	            if (counts.size <= 2 && stateCount >= 2 && stateCount <= 3) {
	                flagColumns.push({
	                    column,
	                    states: DnpColumnCandidateFinder.sortStates(counts),
	                    absentCount,
	                    score: DnpColumnCandidateFinder.scoreFlagColumn(column, [...counts.keys()]),
	                });
	                continue;
	            }
	            const marked = [...counts].filter(([value]) => dnp_metadata_value_1.DnpMetadataValue.isMarker(value));
	            if (marked.length > 0) {
	                markerColumns.push({
	                    column,
	                    markerCount: marked.reduce((sum, [, count]) => sum + count, 0),
	                    presentCount,
	                    markerExamples: DnpColumnCandidateFinder.sortStates(new Map(marked)).slice(0, DnpColumnCandidateFinder.MARKER_EXAMPLE_LIMIT),
	                });
	            }
	        }
	        flagColumns.sort((a, b) => b.score - a.score ||
	            DnpColumnCandidateFinder.presentCount(b) - DnpColumnCandidateFinder.presentCount(a) ||
	            a.column.localeCompare(b.column));
	        markerColumns.sort((a, b) => b.markerCount - a.markerCount || a.column.localeCompare(b.column));
	        return {
	            moduleCount: metadataRows.length,
	            columnNames: [...valueCounts.keys()].sort((a, b) => a.localeCompare(b)),
	            flagColumns,
	            markerColumns,
	        };
	    }
	    static countValues(metadataRows) {
	        const valueCounts = new Map();
	        for (const metadata of metadataRows) {
	            for (const [column, raw] of Object.entries(metadata ?? {})) {
	                if (DnpColumnCandidateFinder.PARSER_KEY_PREFIXES.some((prefix) => column.startsWith(prefix))) {
	                    continue;
	                }
	                const counts = valueCounts.get(column) ?? new Map();
	                valueCounts.set(column, counts);
	                const value = dnp_metadata_value_1.DnpMetadataValue.normalize(raw);
	                if (value !== null) {
	                    counts.set(value, (counts.get(value) ?? 0) + 1);
	                }
	            }
	        }
	        return valueCounts;
	    }
	    static scoreFlagColumn(column, values) {
	        const compactName = column.toLowerCase().replace(/[^a-z0-9]/g, '');
	        let score = 0;
	        if (DnpColumnCandidateFinder.STRONG_NAME_PATTERN.test(compactName)) {
	            score += 2;
	        }
	        else if (DnpColumnCandidateFinder.WEAK_NAME_PATTERN.test(compactName)) {
	            score += 1;
	        }
	        if (values.some((value) => dnp_metadata_value_1.DnpMetadataValue.isMarker(value))) {
	            score += 3;
	        }
	        else if (values.some((value) => DnpColumnCandidateFinder.FLAG_VALUES.has(value))) {
	            score += 1;
	        }
	        return score;
	    }
	    static sortStates(counts) {
	        return [...counts]
	            .map(([value, count]) => ({ value, count }))
	            .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
	    }
	    static presentCount(candidate) {
	        return candidate.states.reduce((sum, state) => sum + state.count, 0);
	    }
	}
	dnpColumnCandidateFinder.DnpColumnCandidateFinder = DnpColumnCandidateFinder;
	
	return dnpColumnCandidateFinder;
}

var dnpColumnDecision = {};

var hasRequiredDnpColumnDecision;

function requireDnpColumnDecision () {
	if (hasRequiredDnpColumnDecision) return dnpColumnDecision;
	hasRequiredDnpColumnDecision = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.dnpColumnDecisionSchema = exports.dnpNoColumnDecisionSchema = exports.dnpColumnsDecisionSchema = exports.DnpColumnDecisionKind = exports.dnpColumnRuleSchema = exports.dnpMarkerRuleSchema = exports.dnpEqualsRuleSchema = exports.DnpColumnRuleKind = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		var DnpColumnRuleKind;
		(function (DnpColumnRuleKind) {
		    DnpColumnRuleKind["Equals"] = "equals";
		    DnpColumnRuleKind["Marker"] = "marker";
		})(DnpColumnRuleKind || (exports.DnpColumnRuleKind = DnpColumnRuleKind = {}));
		exports.dnpEqualsRuleSchema = zod_1.z.object({
		    kind: zod_1.z.literal(DnpColumnRuleKind.Equals),
		    column: zod_1.z.string().min(1),
		    positiveValues: zod_1.z.array(zod_1.z.string().min(1)).min(1),
		    negativeValue: zod_1.z.string().min(1).nullable(),
		});
		exports.dnpMarkerRuleSchema = zod_1.z.object({
		    kind: zod_1.z.literal(DnpColumnRuleKind.Marker),
		    column: zod_1.z.string().min(1),
		});
		exports.dnpColumnRuleSchema = zod_1.z.discriminatedUnion('kind', [
		    exports.dnpEqualsRuleSchema,
		    exports.dnpMarkerRuleSchema,
		]);
		var DnpColumnDecisionKind;
		(function (DnpColumnDecisionKind) {
		    DnpColumnDecisionKind["Columns"] = "columns";
		    DnpColumnDecisionKind["NoDnpColumn"] = "no_dnp_column";
		})(DnpColumnDecisionKind || (exports.DnpColumnDecisionKind = DnpColumnDecisionKind = {}));
		exports.dnpColumnsDecisionSchema = zod_1.z.object({
		    kind: zod_1.z.literal(DnpColumnDecisionKind.Columns),
		    rules: zod_1.z.array(exports.dnpColumnRuleSchema).min(1),
		});
		exports.dnpNoColumnDecisionSchema = zod_1.z.object({
		    kind: zod_1.z.literal(DnpColumnDecisionKind.NoDnpColumn),
		});
		exports.dnpColumnDecisionSchema = zod_1.z.discriminatedUnion('kind', [
		    exports.dnpColumnsDecisionSchema,
		    exports.dnpNoColumnDecisionSchema,
		]);
		
	} (dnpColumnDecision));
	return dnpColumnDecision;
}

var dnpColumnDecisionApplier = {};

var hasRequiredDnpColumnDecisionApplier;

function requireDnpColumnDecisionApplier () {
	if (hasRequiredDnpColumnDecisionApplier) return dnpColumnDecisionApplier;
	hasRequiredDnpColumnDecisionApplier = 1;
	Object.defineProperty(dnpColumnDecisionApplier, "__esModule", { value: true });
	dnpColumnDecisionApplier.DnpColumnDecisionApplier = void 0;
	const dnp_column_decision_1 = requireDnpColumnDecision();
	const dnp_metadata_value_1 = requireDnpMetadataValue();
	var DnpRuleVerdict;
	(function (DnpRuleVerdict) {
	    DnpRuleVerdict["NotFitted"] = "not_fitted";
	    DnpRuleVerdict["Fitted"] = "fitted";
	    DnpRuleVerdict["NoOpinion"] = "no_opinion";
	})(DnpRuleVerdict || (DnpRuleVerdict = {}));
	class DnpColumnDecisionApplier {
	    static derive(decision, metadata) {
	        if (decision.kind === dnp_column_decision_1.DnpColumnDecisionKind.NoDnpColumn) {
	            return { isDnp: false, sourceColumn: null };
	        }
	        for (const rule of decision.rules) {
	            const verdict = DnpColumnDecisionApplier.judge(rule, metadata?.[rule.column]);
	            if (verdict !== DnpRuleVerdict.NoOpinion) {
	                return { isDnp: verdict === DnpRuleVerdict.NotFitted, sourceColumn: rule.column };
	            }
	        }
	        return { isDnp: false, sourceColumn: null };
	    }
	    static judge(rule, raw) {
	        const value = dnp_metadata_value_1.DnpMetadataValue.normalize(raw);
	        if (value === null) {
	            return DnpRuleVerdict.NoOpinion;
	        }
	        if (rule.kind === dnp_column_decision_1.DnpColumnRuleKind.Marker) {
	            return dnp_metadata_value_1.DnpMetadataValue.isMarker(value) ? DnpRuleVerdict.NotFitted : DnpRuleVerdict.NoOpinion;
	        }
	        if (rule.positiveValues.some((positive) => dnp_metadata_value_1.DnpMetadataValue.normalize(positive) === value)) {
	            return DnpRuleVerdict.NotFitted;
	        }
	        if (rule.negativeValue !== null && dnp_metadata_value_1.DnpMetadataValue.normalize(rule.negativeValue) === value) {
	            return DnpRuleVerdict.Fitted;
	        }
	        return DnpRuleVerdict.NoOpinion;
	    }
	}
	dnpColumnDecisionApplier.DnpColumnDecisionApplier = DnpColumnDecisionApplier;
	
	return dnpColumnDecisionApplier;
}

var dnpColumnFixture = {};

var hasRequiredDnpColumnFixture;

function requireDnpColumnFixture () {
	if (hasRequiredDnpColumnFixture) return dnpColumnFixture;
	hasRequiredDnpColumnFixture = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.dnpColumnFixtureSchema = exports.dnpColumnFixtureExpectationSchema = exports.dnpColumnFixtureModuleSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const dnp_column_decision_1 = requireDnpColumnDecision();
		exports.dnpColumnFixtureModuleSchema = zod_1.z.object({
		    referenceDesignator: zod_1.z.string(),
		    isDnp: zod_1.z.boolean().optional(),
		    metadata: sigma_engine_shared_1.DesignModels.MetadataSchema,
		});
		exports.dnpColumnFixtureExpectationSchema = zod_1.z.object({
		    decision: dnp_column_decision_1.dnpColumnDecisionSchema,
		    dnpReferenceDesignators: zod_1.z.array(zod_1.z.string()),
		});
		exports.dnpColumnFixtureSchema = zod_1.z.object({
		    name: zod_1.z.string(),
		    description: zod_1.z.string(),
		    cadTool: sigma_engine_shared_1.DesignModels.CadToolSchema,
		    expectedAnswerConfirmed: zod_1.z.boolean(),
		    modules: zod_1.z.array(exports.dnpColumnFixtureModuleSchema),
		    expected: exports.dnpColumnFixtureExpectationSchema.optional(),
		});
		
	} (dnpColumnFixture));
	return dnpColumnFixture;
}

var dnpColumnShortlist = {};

var hasRequiredDnpColumnShortlist;

function requireDnpColumnShortlist () {
	if (hasRequiredDnpColumnShortlist) return dnpColumnShortlist;
	hasRequiredDnpColumnShortlist = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.dnpColumnClassificationSchema = exports.dnpColumnShortlistEntrySchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const dnp_classification_gate_1 = requireDnpClassificationGate();
		const dnp_column_candidate_finder_1 = requireDnpColumnCandidateFinder();
		const dnp_column_decision_1 = requireDnpColumnDecision();
		exports.dnpColumnShortlistEntrySchema = zod_1.z.object({
		    rule: dnp_column_decision_1.dnpColumnRuleSchema,
		    confidence: zod_1.z.number().min(0).max(1),
		    reason: zod_1.z.string(),
		    states: zod_1.z.array(dnp_column_candidate_finder_1.dnpColumnStateSchema),
		    absentCount: zod_1.z.number().int().nonnegative(),
		    markerCount: zod_1.z.number().int().nonnegative().nullable(),
		});
		exports.dnpColumnClassificationSchema = zod_1.z.object({
		    outcome: zod_1.z.nativeEnum(dnp_classification_gate_1.DnpClassificationOutcome),
		    shortlist: zod_1.z.array(exports.dnpColumnShortlistEntrySchema),
		    columnNames: zod_1.z.array(zod_1.z.string()),
		});
		
	} (dnpColumnShortlist));
	return dnpColumnShortlist;
}

var hasRequiredDnpColumns;

function requireDnpColumns () {
	if (hasRequiredDnpColumns) return DnpColumns;
	hasRequiredDnpColumns = 1;
	(function (exports) {
		var __createBinding = (DnpColumns && DnpColumns.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (DnpColumns && DnpColumns.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireDnpClassificationGate(), exports);
		__exportStar(requireDnpColumnCandidateFinder(), exports);
		__exportStar(requireDnpColumnDecision(), exports);
		__exportStar(requireDnpColumnDecisionApplier(), exports);
		__exportStar(requireDnpColumnFixture(), exports);
		__exportStar(requireDnpColumnShortlist(), exports);
		__exportStar(requireDnpMetadataValue(), exports);
		
	} (DnpColumns));
	return DnpColumns;
}

var Gingersnap$1 = {};

var scrapeLinkCandidates_contract = {};

var hasRequiredScrapeLinkCandidates_contract;

function requireScrapeLinkCandidates_contract () {
	if (hasRequiredScrapeLinkCandidates_contract) return scrapeLinkCandidates_contract;
	hasRequiredScrapeLinkCandidates_contract = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ScrapeLinkCandidatesWorkflowOutputSchema = exports.LinkCandidateSchema = exports.ScrapeLinkCandidatesWorkflowInputSchema = exports.SCRAPE_LINK_CANDIDATES_WORKFLOW_NAME = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.SCRAPE_LINK_CANDIDATES_WORKFLOW_NAME = 'scrapeLinkCandidatesWorkflow';
		exports.ScrapeLinkCandidatesWorkflowInputSchema = zod_1.z.object({
		    url: zod_1.z.string(),
		    timeoutMs: zod_1.z.number().optional(),
		});
		exports.LinkCandidateSchema = zod_1.z.object({
		    text: zod_1.z.string(),
		    href: zod_1.z.string(),
		});
		exports.ScrapeLinkCandidatesWorkflowOutputSchema = zod_1.z.object({
		    statusCode: zod_1.z.number(),
		    finalUrl: zod_1.z.string(),
		    title: zod_1.z.string(),
		    contentType: zod_1.z.string().optional(),
		    links: zod_1.z.array(exports.LinkCandidateSchema),
		});
		
	} (scrapeLinkCandidates_contract));
	return scrapeLinkCandidates_contract;
}

var hasRequiredGingersnap$1;

function requireGingersnap$1 () {
	if (hasRequiredGingersnap$1) return Gingersnap$1;
	hasRequiredGingersnap$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DownloadFailureSchema = exports.DownloadFailureReason = exports.StoredDownloadResultSchema = exports.DownloadSummarySchema = exports.HtmlPageSummarySchema = exports.GingersnapTransportName = exports.FileType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const scrape_link_candidates_contract_1 = requireScrapeLinkCandidates_contract();
		var FileType;
		(function (FileType) {
		    FileType["Board"] = "board";
		    FileType["Schematic"] = "schematic";
		})(FileType || (exports.FileType = FileType = {}));
		var GingersnapTransportName;
		(function (GingersnapTransportName) {
		    GingersnapTransportName["Http"] = "http";
		    GingersnapTransportName["Temporal"] = "temporal";
		})(GingersnapTransportName || (exports.GingersnapTransportName = GingersnapTransportName = {}));
		exports.HtmlPageSummarySchema = zod_1.default.object({
		    finalUrl: zod_1.default.string(),
		    title: zod_1.default.string(),
		    links: zod_1.default.array(scrape_link_candidates_contract_1.LinkCandidateSchema),
		});
		exports.DownloadSummarySchema = zod_1.default.object({
		    statusCode: zod_1.default.number(),
		    fileName: zod_1.default.string().optional(),
		    size: zod_1.default.number(),
		    message: zod_1.default.string().optional(),
		});
		exports.StoredDownloadResultSchema = exports.DownloadSummarySchema.extend({
		    location: zod_1.default.string(),
		});
		var DownloadFailureReason;
		(function (DownloadFailureReason) {
		    DownloadFailureReason["UpstreamStatus"] = "upstream_status";
		    DownloadFailureReason["HtmlPage"] = "html_page";
		    DownloadFailureReason["NotAPdf"] = "not_a_pdf";
		    DownloadFailureReason["ConnectionFailed"] = "connection_failed";
		})(DownloadFailureReason || (exports.DownloadFailureReason = DownloadFailureReason = {}));
		exports.DownloadFailureSchema = zod_1.default.object({
		    reason: zod_1.default.nativeEnum(DownloadFailureReason),
		    statusCode: zod_1.default.number(),
		    message: zod_1.default.string(),
		    page: exports.HtmlPageSummarySchema.optional(),
		    fileName: zod_1.default.string().optional(),
		});
		
	} (Gingersnap$1));
	return Gingersnap$1;
}

var Gingersnap = {};

var taskQueue = {};

var hasRequiredTaskQueue;

function requireTaskQueue () {
	if (hasRequiredTaskQueue) return taskQueue;
	hasRequiredTaskQueue = 1;
	Object.defineProperty(taskQueue, "__esModule", { value: true });
	taskQueue.GINGERSNAP_DOWNLOADS_LOCATION_PREFIX = taskQueue.GINGERSNAP_TASK_QUEUE = void 0;
	taskQueue.GINGERSNAP_TASK_QUEUE = 'GINGERSNAP_QUEUE';
	taskQueue.GINGERSNAP_DOWNLOADS_LOCATION_PREFIX = 'gingersnap:downloads/';
	
	return taskQueue;
}

var fetchPdf_contract = {};

var hasRequiredFetchPdf_contract;

function requireFetchPdf_contract () {
	if (hasRequiredFetchPdf_contract) return fetchPdf_contract;
	hasRequiredFetchPdf_contract = 1;
	Object.defineProperty(fetchPdf_contract, "__esModule", { value: true });
	fetchPdf_contract.FetchPdfStoredWorkflowOutputSchema = fetchPdf_contract.FetchPdfWorkflowOutputSchema = fetchPdf_contract.FetchPdfWorkflowInputSchema = fetchPdf_contract.FETCH_PDF_WORKFLOW_NAME = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	fetchPdf_contract.FETCH_PDF_WORKFLOW_NAME = 'fetchPdfWorkflow';
	fetchPdf_contract.FetchPdfWorkflowInputSchema = zod_1.z.object({
	    url: zod_1.z.string(),
	    timeoutMs: zod_1.z.number().optional(),
	    bailIfHtmlAfterMs: zod_1.z.number().nullable().optional(),
	    userAgent: zod_1.z.string().optional(),
	    proxy: zod_1.z
	        .object({
	        server: zod_1.z.string(),
	        username: zod_1.z.string().optional(),
	        password: zod_1.z.string().optional(),
	    })
	        .optional(),
	    presignGrant: zod_1.z.string().optional(),
	});
	fetchPdf_contract.FetchPdfWorkflowOutputSchema = zod_1.z.object({
	    statusCode: zod_1.z.number(),
	    headers: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
	    body: zod_1.z.string(),
	    fileName: zod_1.z.string().optional(),
	});
	fetchPdf_contract.FetchPdfStoredWorkflowOutputSchema = zod_1.z.object({
	    statusCode: zod_1.z.number(),
	    headers: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
	    location: zod_1.z.string(),
	    size: zod_1.z.number(),
	    fileName: zod_1.z.string().optional(),
	});
	
	return fetchPdf_contract;
}

var renderImages_contract = {};

var hasRequiredRenderImages_contract;

function requireRenderImages_contract () {
	if (hasRequiredRenderImages_contract) return renderImages_contract;
	hasRequiredRenderImages_contract = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RenderImagesStoredWorkflowOutputSchema = exports.StoredDesignRenderSchema = exports.RenderImagesWorkflowInputSchema = exports.RENDER_IMAGES_WORKFLOW_NAME = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.RENDER_IMAGES_WORKFLOW_NAME = 'renderImagesWorkflow';
		exports.RenderImagesWorkflowInputSchema = zod_1.z.object({
		    dataLocation: zod_1.z.string(),
		    outputLocationPrefix: zod_1.z.string().endsWith('/'),
		    renderDesignIds: zod_1.z.string().array().optional(),
		    dimensions: zod_1.z.object({ x: zod_1.z.number(), y: zod_1.z.number() }).array().optional(),
		    presignGrant: zod_1.z.string().min(1),
		});
		exports.StoredDesignRenderSchema = zod_1.z.object({
		    dimensions: zod_1.z.object({ x: zod_1.z.number(), y: zod_1.z.number() }),
		    location: zod_1.z.string(),
		    size: zod_1.z.number(),
		    fileType: zod_1.z.string(),
		    mimeType: zod_1.z.string(),
		});
		exports.RenderImagesStoredWorkflowOutputSchema = zod_1.z.object({
		    designRenders: zod_1.z.record(zod_1.z.string(), exports.StoredDesignRenderSchema.array()),
		});
		
	} (renderImages_contract));
	return renderImages_contract;
}

var hasRequiredGingersnap;

function requireGingersnap () {
	if (hasRequiredGingersnap) return Gingersnap;
	hasRequiredGingersnap = 1;
	(function (exports) {
		var __createBinding = (Gingersnap && Gingersnap.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Gingersnap && Gingersnap.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireTaskQueue(), exports);
		__exportStar(requireFetchPdf_contract(), exports);
		__exportStar(requireRenderImages_contract(), exports);
		__exportStar(requireScrapeLinkCandidates_contract(), exports);
		
	} (Gingersnap));
	return Gingersnap;
}

var ImplementationRules = {};

var canonicalImplementationRules = {};

var implementationRules = {};

var deriveRuleModality = {};

var hasRequiredDeriveRuleModality;

function requireDeriveRuleModality () {
	if (hasRequiredDeriveRuleModality) return deriveRuleModality;
	hasRequiredDeriveRuleModality = 1;
	Object.defineProperty(deriveRuleModality, "__esModule", { value: true });
	deriveRuleModality.modalPhrases = modalPhrases;
	deriveRuleModality.deriveRuleModality = deriveRuleModality$1;
	const implementation_rules_1 = requireImplementationRules$1();
	function modalPhrases() {
	    return [
	        { modality: implementation_rules_1.RuleModality.MustNot, phrase: 'must not', pattern: /\bmust not\b/i },
	        { modality: implementation_rules_1.RuleModality.ShouldNot, phrase: 'should not', pattern: /\bshould not\b/i },
	        { modality: implementation_rules_1.RuleModality.Must, phrase: 'must', pattern: /\bmust\b/i },
	        { modality: implementation_rules_1.RuleModality.Should, phrase: 'should', pattern: /\bshould\b/i },
	    ];
	}
	function deriveRuleModality$1(ruleText) {
	    for (const { pattern, modality } of modalPhrases()) {
	        if (pattern.test(ruleText)) {
	            return { kind: 'derived', modality };
	        }
	    }
	    return { kind: 'underivable' };
	}
	
	return deriveRuleModality;
}

var ruleCitation = {};

var hasRequiredRuleCitation;

function requireRuleCitation () {
	if (hasRequiredRuleCitation) return ruleCitation;
	hasRequiredRuleCitation = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.RuleCitationSchema = void 0;
		exports.toPdfCitationDirectiveArgs = toPdfCitationDirectiveArgs;
		exports.fromPdfCitationDirectiveArgs = fromPdfCitationDirectiveArgs;
		const z = /*@__PURE__*/ requireZod();
		function normalizeCitationInput(value) {
		    if (value === null || typeof value !== 'object') {
		        return value;
		    }
		    const record = { ...value };
		    const indexes = record['elementIndexes'];
		    if (Array.isArray(indexes) && indexes.every((index) => typeof index === 'number')) {
		        record['elementIndexes'] = [...new Set(indexes)].sort((left, right) => left - right);
		    }
		    return record;
		}
		exports.RuleCitationSchema = z.preprocess(normalizeCitationInput, z
		    .object({
		    citationText: z.string(),
		    fileId: z.string().trim().min(1),
		    elementIndexes: z.number().int().nonnegative().array().min(1).nullable(),
		    pageNumber: z.number().int().positive().nullable(),
		    citedText: z.string().nullable(),
		    description: z.string().nullable(),
		})
		    .strict()
		    .superRefine((citation, context) => {
		    if (citation.elementIndexes === null && (citation.citedText === null || citation.citedText.trim() === '')) {
		        context.addIssue({
		            code: z.ZodIssueCode.custom,
		            message: 'A rule citation requires at least one elementIndex or citedText; page-only citations are not valid rules',
		        });
		    }
		}));
		function toPdfCitationDirectiveArgs(citation) {
		    const parsed = exports.RuleCitationSchema.parse(citation);
		    return {
		        citationText: parsed.citationText,
		        fileId: parsed.fileId,
		        elementIndexes: parsed.elementIndexes,
		        pageNumber: parsed.pageNumber,
		        citedText: parsed.citedText,
		        description: parsed.description,
		    };
		}
		function fromPdfCitationDirectiveArgs(citation) {
		    return exports.RuleCitationSchema.parse({
		        citationText: citation.citationText,
		        fileId: citation.fileId,
		        elementIndexes: citation.elementIndexes ??
		            (citation.elementIndex === undefined ? null
		                : citation.elementIndex === null ? null
		                    : [citation.elementIndex]),
		        pageNumber: citation.pageNumber ?? null,
		        citedText: citation.citedText ?? null,
		        description: citation.description ?? null,
		    });
		}
		
	} (ruleCitation));
	return ruleCitation;
}

var hasRequiredImplementationRules$1;

function requireImplementationRules$1 () {
	if (hasRequiredImplementationRules$1) return implementationRules;
	hasRequiredImplementationRules$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ImplementationRulesAttemptSchema = exports.RulesRunOutcomeSchema = exports.RulesRunOutcome = exports.RulesRunCoverageSchema = exports.RulesRunCountsSchema = exports.ImplementationRuleSectionSchema = exports.ImplementationRuleSchema = exports.RuleModalitySchema = exports.RuleModality = void 0;
		const z = /*@__PURE__*/ requireZod();
		const derive_rule_modality_1 = requireDeriveRuleModality();
		const rule_citation_1 = requireRuleCitation();
		var RuleModality;
		(function (RuleModality) {
		    RuleModality["Must"] = "must";
		    RuleModality["MustNot"] = "must-not";
		    RuleModality["Should"] = "should";
		    RuleModality["ShouldNot"] = "should-not";
		})(RuleModality || (exports.RuleModality = RuleModality = {}));
		exports.RuleModalitySchema = z.nativeEnum(RuleModality);
		exports.ImplementationRuleSchema = z
		    .object({
		    id: z.string(),
		    text: z.string(),
		    modality: exports.RuleModalitySchema,
		    citations: rule_citation_1.RuleCitationSchema.array().min(1),
		})
		    .superRefine((rule, context) => {
		    const derivation = (0, derive_rule_modality_1.deriveRuleModality)(rule.text);
		    if (derivation.kind !== 'derived' || derivation.modality !== rule.modality) {
		        context.addIssue({
		            code: z.ZodIssueCode.custom,
		            path: ['modality'],
		            message: 'Rule modality must equal the modality derived from rule text',
		        });
		    }
		});
		exports.ImplementationRuleSectionSchema = z.object({
		    sectionId: z.string(),
		    sectionSlug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
		    rules: exports.ImplementationRuleSchema.array(),
		});
		exports.RulesRunCountsSchema = z.object({
		    factualErrors: z.number().int().nonnegative(),
		    pinErrors: z.number().int().nonnegative(),
		    polarityErrors: z.number().int().nonnegative(),
		    gaps: z.number().int().nonnegative(),
		    materialInventedRules: z.number().int().nonnegative(),
		    scopeViolations: z.number().int().nonnegative(),
		    formatIssues: z.number().int().nonnegative(),
		    unresolvedConflicts: z.number().int().nonnegative(),
		    droppedCandidates: z.number().int().nonnegative(),
		    underivableCandidates: z.number().int().nonnegative(),
		    fmCounts: z.record(z.string(), z.number().int().nonnegative()),
		});
		exports.RulesRunCoverageSchema = z.object({
		    totalUnits: z.number().int().nonnegative(),
		    completedUnits: z.number().int().nonnegative(),
		    excludedUnits: z.number().int().nonnegative(),
		    failedUnits: z.number().int().nonnegative(),
		});
		var RulesRunOutcome;
		(function (RulesRunOutcome) {
		    RulesRunOutcome["Complete"] = "complete";
		    RulesRunOutcome["NoDatasheet"] = "no-datasheet";
		    RulesRunOutcome["WrongDocument"] = "wrong-document";
		    RulesRunOutcome["DegradedExtraction"] = "degraded-extraction";
		    RulesRunOutcome["Failed"] = "failed";
		})(RulesRunOutcome || (exports.RulesRunOutcome = RulesRunOutcome = {}));
		exports.RulesRunOutcomeSchema = z.nativeEnum(RulesRunOutcome);
		exports.ImplementationRulesAttemptSchema = z
		    .object({
		    id: z.string(),
		    outcome: exports.RulesRunOutcomeSchema,
		    startedAt: z.string().datetime(),
		    completedAt: z.string().datetime().nullable(),
		})
		    .superRefine((attempt, context) => {
		    if (attempt.completedAt !== null && Date.parse(attempt.completedAt) < Date.parse(attempt.startedAt)) {
		        context.addIssue({
		            code: z.ZodIssueCode.custom,
		            path: ['completedAt'],
		            message: 'Attempt completion cannot precede its start',
		        });
		    }
		});
		
	} (implementationRules));
	return implementationRules;
}

var hasRequiredCanonicalImplementationRules;

function requireCanonicalImplementationRules () {
	if (hasRequiredCanonicalImplementationRules) return canonicalImplementationRules;
	hasRequiredCanonicalImplementationRules = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ImplementationRuleSectionsSchema = void 0;
		exports.parseImplementationRuleSections = parseImplementationRuleSections;
		exports.computeImplementationRuleSectionsContentHash = computeImplementationRuleSectionsContentHash;
		const z = /*@__PURE__*/ requireZod();
		const implementation_rules_1 = requireImplementationRules$1();
		exports.ImplementationRuleSectionsSchema = implementation_rules_1.ImplementationRuleSectionSchema.array().superRefine((sections, context) => {
		    const seenSectionIds = new Set();
		    const seenSlugs = new Set();
		    const seenRuleIds = new Set();
		    for (const [sectionIndex, section] of sections.entries()) {
		        if (seenSectionIds.has(section.sectionId)) {
		            context.addIssue({
		                code: z.ZodIssueCode.custom,
		                path: [sectionIndex, 'sectionId'],
		                message: `Duplicate implementation-rule section id: ${section.sectionId}`,
		            });
		        }
		        seenSectionIds.add(section.sectionId);
		        if (seenSlugs.has(section.sectionSlug)) {
		            context.addIssue({
		                code: z.ZodIssueCode.custom,
		                path: [sectionIndex, 'sectionSlug'],
		                message: `Duplicate implementation-rule section slug: ${section.sectionSlug}`,
		            });
		        }
		        seenSlugs.add(section.sectionSlug);
		        for (const [ruleIndex, rule] of section.rules.entries()) {
		            if (seenRuleIds.has(rule.id)) {
		                context.addIssue({
		                    code: z.ZodIssueCode.custom,
		                    path: [sectionIndex, 'rules', ruleIndex, 'id'],
		                    message: `Duplicate implementation-rule id: ${rule.id}`,
		                });
		            }
		            seenRuleIds.add(rule.id);
		        }
		    }
		});
		function parseImplementationRuleSections(value) {
		    return value === undefined ? [] : exports.ImplementationRuleSectionsSchema.parse(value);
		}
		async function computeImplementationRuleSectionsContentHash(sections) {
		    const byCodePoint = (left, right) => left < right ? -1
		        : left > right ? 1
		            : 0;
		    const projection = [...sections]
		        .sort((left, right) => byCodePoint(left.sectionId, right.sectionId))
		        .map((section) => ({
		        sectionId: section.sectionId,
		        sectionSlug: section.sectionSlug,
		        rules: [...section.rules]
		            .sort((left, right) => byCodePoint(left.id, right.id))
		            .map((rule) => ({
		            id: rule.id,
		            text: rule.text,
		            citations: rule.citations.map((citation) => ({
		                citationText: citation.citationText,
		                fileId: citation.fileId,
		                elementIndexes: citation.elementIndexes,
		                pageNumber: citation.pageNumber,
		                citedText: citation.citedText,
		                description: citation.description,
		            })),
		        })),
		    }));
		    const bytes = new TextEncoder().encode(JSON.stringify(projection));
		    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
		    const hexadecimal = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
		    return `sha256:${hexadecimal}`;
		}
		
	} (canonicalImplementationRules));
	return canonicalImplementationRules;
}

var implementationRuleIds = {};

var hasRequiredImplementationRuleIds;

function requireImplementationRuleIds () {
	if (hasRequiredImplementationRuleIds) return implementationRuleIds;
	hasRequiredImplementationRuleIds = 1;
	Object.defineProperty(implementationRuleIds, "__esModule", { value: true });
	implementationRuleIds.ImplementationRuleIds = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	class ImplementationRuleIds {
	    static { this.RULE_ID_PREFIX = 'imrrule_'; }
	    static { this.SECTION_ID_PREFIX = 'imrsec_'; }
	    static { this.DRAFT_SECTION_ID_PREFIX = 'draft_section_'; }
	    static { this.OPERATOR_RULE_RANDOM_LENGTH = 36; }
	    static { this.DRAFT_SECTION_RANDOM_LENGTH = 12; }
	    static mintOperatorRuleId() {
	        return (ImplementationRuleIds.RULE_ID_PREFIX + sigma_engine_shared_1.Utils.randomAlphaStr(ImplementationRuleIds.OPERATOR_RULE_RANDOM_LENGTH));
	    }
	    static mintDraftSectionId() {
	        return (ImplementationRuleIds.DRAFT_SECTION_ID_PREFIX +
	            sigma_engine_shared_1.Utils.randomAlphaStr(ImplementationRuleIds.DRAFT_SECTION_RANDOM_LENGTH));
	    }
	}
	implementationRuleIds.ImplementationRuleIds = ImplementationRuleIds;
	
	return implementationRuleIds;
}

var implementationRulesHistoryEvent = {};

var hasRequiredImplementationRulesHistoryEvent;

function requireImplementationRulesHistoryEvent () {
	if (hasRequiredImplementationRulesHistoryEvent) return implementationRulesHistoryEvent;
	hasRequiredImplementationRulesHistoryEvent = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ImplementationRulesHistoryEventSchema = exports.ImplementationRulesExtractionAttemptHistoryEventSchema = exports.ImplementationRulesHistoryResultSchema = exports.ImplementationRulesHistoryGeneratorSchema = exports.ImplementationRulesHistorySourceSchema = exports.RulesVerdict = void 0;
		exports.computeRulesVerdict = computeRulesVerdict;
		const z = /*@__PURE__*/ requireZod();
		const implementation_rules_1 = requireImplementationRules$1();
		var RulesVerdict;
		(function (RulesVerdict) {
		    RulesVerdict["Pass"] = "pass";
		    RulesVerdict["ConditionalPass"] = "conditional-pass";
		    RulesVerdict["NeedsRevision"] = "needs-revision";
		    RulesVerdict["NeedsRework"] = "needs-rework";
		})(RulesVerdict || (exports.RulesVerdict = RulesVerdict = {}));
		function computeRulesVerdict(counts) {
		    if (counts.pinErrors > 0 ||
		        counts.polarityErrors > 0 ||
		        counts.materialInventedRules > 0 ||
		        counts.factualErrors > 0) {
		        return RulesVerdict.NeedsRework;
		    }
		    if (counts.gaps > 0) {
		        return RulesVerdict.NeedsRevision;
		    }
		    if (counts.scopeViolations > 0 || counts.formatIssues > 0) {
		        return RulesVerdict.ConditionalPass;
		    }
		    return RulesVerdict.Pass;
		}
		exports.ImplementationRulesHistorySourceSchema = z
		    .object({
		    fileId: z.string().nullable(),
		    fingerprint: z.string().nullable(),
		})
		    .strict();
		exports.ImplementationRulesHistoryGeneratorSchema = z
		    .object({
		    extractorVersion: z.number().int().nonnegative(),
		    variantId: z.string(),
		    configHash: z.string(),
		    promptVariant: z.string(),
		    modelCard: z.string(),
		    effort: z.string(),
		    workflowRunId: z.string(),
		})
		    .strict();
		const HistoryResultBaseSchema = z.object({
		    counts: implementation_rules_1.RulesRunCountsSchema,
		    verdict: z.nativeEnum(RulesVerdict),
		    coverage: implementation_rules_1.RulesRunCoverageSchema,
		    contentHash: z.string(),
		    createdAt: z.string().datetime(),
		});
		exports.ImplementationRulesHistoryResultSchema = z.discriminatedUnion('disposition', [
		    HistoryResultBaseSchema.extend({
		        disposition: z.literal('committed'),
		        retainedFromResolutionEntryId: z.never().optional(),
		    }).strict(),
		    HistoryResultBaseSchema.extend({
		        disposition: z.literal('retained'),
		        retainedFromResolutionEntryId: z.string(),
		    }).strict(),
		    HistoryResultBaseSchema.extend({
		        disposition: z.literal('recorded'),
		        retainedFromResolutionEntryId: z.never().optional(),
		    }).strict(),
		]);
		exports.ImplementationRulesExtractionAttemptHistoryEventSchema = z
		    .object({
		    kind: z.literal('extraction-attempt'),
		    attempt: implementation_rules_1.ImplementationRulesAttemptSchema,
		    source: exports.ImplementationRulesHistorySourceSchema,
		    generator: exports.ImplementationRulesHistoryGeneratorSchema,
		    result: exports.ImplementationRulesHistoryResultSchema,
		})
		    .strict();
		exports.ImplementationRulesHistoryEventSchema = z.discriminatedUnion('kind', [
		    exports.ImplementationRulesExtractionAttemptHistoryEventSchema,
		    z
		        .object({
		        kind: z.literal('operator-edit'),
		        baseContentHash: z.string(),
		        resultContentHash: z.string(),
		        createdRuleIds: z.string().array(),
		        rulesOnlyEntryId: z.string().optional(),
		    })
		        .strict(),
		]);
		
	} (implementationRulesHistoryEvent));
	return implementationRulesHistoryEvent;
}

var normativeSentenceDetector = {};

var sentenceSplitter = {};

var hasRequiredSentenceSplitter;

function requireSentenceSplitter () {
	if (hasRequiredSentenceSplitter) return sentenceSplitter;
	hasRequiredSentenceSplitter = 1;
	Object.defineProperty(sentenceSplitter, "__esModule", { value: true });
	sentenceSplitter.SentenceSplitter = void 0;
	class SentenceSplitter {
	    static { this.BOUNDARY_PATTERN = /(?<=[.!?])\s+(?=[A-Z(])/; }
	    static split(text) {
	        return text
	            .split(SentenceSplitter.BOUNDARY_PATTERN)
	            .map((sentence) => sentence.trim())
	            .filter((sentence) => sentence !== '');
	    }
	}
	sentenceSplitter.SentenceSplitter = SentenceSplitter;
	
	return sentenceSplitter;
}

var hasRequiredNormativeSentenceDetector;

function requireNormativeSentenceDetector () {
	if (hasRequiredNormativeSentenceDetector) return normativeSentenceDetector;
	hasRequiredNormativeSentenceDetector = 1;
	Object.defineProperty(normativeSentenceDetector, "__esModule", { value: true });
	normativeSentenceDetector.NormativeSentenceDetector = void 0;
	const sentence_splitter_1 = requireSentenceSplitter();
	class NormativeSentenceDetector {
	    static { this.PHRASES = [
	        'must(?: not)?',
	        'should(?: not)?',
	        'shall',
	        'recommend(?:s|ed|ation)?',
	        'requir(?:e[sd]?|ing)',
	        'do not',
	        'never',
	        'ensure[sd]?',
	        'connect(?:s|ed)?',
	        'tied?',
	        'place[sd]?',
	        'use[d]?(?! of\\b)',
	        'keeps?',
	        'selects?',
	        'avoids?',
	    ]; }
	    static { this.TEST_PATTERN = NormativeSentenceDetector.phrasePattern('i'); }
	    static matches(sentence) {
	        return NormativeSentenceDetector.TEST_PATTERN.test(sentence);
	    }
	    static detect(text) {
	        return sentence_splitter_1.SentenceSplitter.split(text).filter((sentence) => NormativeSentenceDetector.matches(sentence));
	    }
	    static highlightPattern() {
	        return NormativeSentenceDetector.phrasePattern('gi');
	    }
	    static phrasePattern(flags) {
	        return new RegExp(`\\b(?:${NormativeSentenceDetector.PHRASES.join('|')})\\b`, flags);
	    }
	}
	normativeSentenceDetector.NormativeSentenceDetector = NormativeSentenceDetector;
	
	return normativeSentenceDetector;
}

var rewriteRuleModality = {};

var hasRequiredRewriteRuleModality;

function requireRewriteRuleModality () {
	if (hasRequiredRewriteRuleModality) return rewriteRuleModality;
	hasRequiredRewriteRuleModality = 1;
	Object.defineProperty(rewriteRuleModality, "__esModule", { value: true });
	rewriteRuleModality.applyRuleModality = applyRuleModality;
	const derive_rule_modality_1 = requireDeriveRuleModality();
	const implementation_rules_1 = requireImplementationRules$1();
	function applyRuleModality(text, modality) {
	    const collapsed = text.replace(/\bnot\s+not\b/gi, 'not');
	    const phrase = phraseFor(modality);
	    const targetRank = rankOf(modality);
	    const occurrences = [...collapsed.matchAll(modalPhrasePattern())].map((match) => ({
	        index: match.index ?? 0,
	        original: match[0],
	        rank: rankOf(modalityOf(match[0])),
	    }));
	    if (occurrences.length === 0) {
	        const trimmed = collapsed.trim();
	        const body = trimmed === '' ? '' : ` ${trimmed.charAt(0).toLowerCase()}${trimmed.slice(1)}`;
	        return /^ not\b/i.test(body) ? `The design ${phrase}:${body}` : `The design ${phrase}${body}`;
	    }
	    const governingRank = Math.min(...occurrences.map((occurrence) => occurrence.rank));
	    const governing = occurrences.find((occurrence) => occurrence.rank === governingRank);
	    let rewritten = '';
	    let cursor = 0;
	    for (const occurrence of occurrences) {
	        rewritten += collapsed.slice(cursor, occurrence.index);
	        const replace = occurrence === governing || occurrence.rank < targetRank;
	        rewritten += replace ? matchFirstLetterCase(phrase, occurrence.original) : occurrence.original;
	        cursor = occurrence.index + occurrence.original.length;
	    }
	    return rewritten + collapsed.slice(cursor);
	}
	function modalPhrasePattern() {
	    return new RegExp(`\\b(${(0, derive_rule_modality_1.modalPhrases)()
	        .map(({ phrase }) => phrase)
	        .join('|')})\\b`, 'gi');
	}
	function phraseFor(modality) {
	    return (0, derive_rule_modality_1.modalPhrases)()[rankOf(modality)].phrase;
	}
	function rankOf(modality) {
	    return (0, derive_rule_modality_1.modalPhrases)().findIndex((candidate) => candidate.modality === modality);
	}
	function modalityOf(phrase) {
	    const normalized = phrase.toLowerCase();
	    return (0, derive_rule_modality_1.modalPhrases)().find((candidate) => candidate.phrase === normalized)?.modality ?? implementation_rules_1.RuleModality.Must;
	}
	function matchFirstLetterCase(phrase, original) {
	    const first = original.charAt(0);
	    return first === first.toUpperCase() ? `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}` : phrase;
	}
	
	return rewriteRuleModality;
}

var ruleNormalization = {};

var hasRequiredRuleNormalization;

function requireRuleNormalization () {
	if (hasRequiredRuleNormalization) return ruleNormalization;
	hasRequiredRuleNormalization = 1;
	Object.defineProperty(ruleNormalization, "__esModule", { value: true });
	ruleNormalization.jaccard = ruleNormalization.SubjectKeySchema = ruleNormalization.SubjectKind = void 0;
	ruleNormalization.subjectSignalsFromDesignSignals = subjectSignalsFromDesignSignals;
	ruleNormalization.subjectKey = subjectKey;
	ruleNormalization.signalSectionSlug = signalSectionSlug;
	ruleNormalization.sectionSlug = sectionSlug;
	ruleNormalization.actionVerbClass = actionVerbClass;
	ruleNormalization.quantityFingerprint = quantityFingerprint;
	ruleNormalization.textJaccard = textJaccard;
	ruleNormalization.normalizeWords = normalizeWords;
	ruleNormalization.normalizeCitationWhitespace = normalizeCitationWhitespace;
	ruleNormalization.normalizedCitationIncludes = normalizedCitationIncludes;
	ruleNormalization.quantityTokens = quantityTokens;
	const z = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	function subjectSignalsFromDesignSignals(signals) {
	    return signals.map((signal) => ({ id: String(signal.id), name: signal.name }));
	}
	var SubjectKind;
	(function (SubjectKind) {
	    SubjectKind["Signal"] = "signal";
	    SubjectKind["Pin"] = "pin";
	    SubjectKind["Rail"] = "rail";
	    SubjectKind["Layout"] = "layout";
	    SubjectKind["Sequencing"] = "sequencing";
	    SubjectKind["Thermal"] = "thermal";
	    SubjectKind["Device"] = "device";
	})(SubjectKind || (ruleNormalization.SubjectKind = SubjectKind = {}));
	ruleNormalization.SubjectKeySchema = z
	    .object({
	    kind: z.nativeEnum(SubjectKind),
	    ref: z.string(),
	})
	    .strict();
	const SIGNAL_SLUG_PREFIX = 'signal';
	const ACTION_VERBS = {
	    connect: ['connect', 'connected', 'tie', 'tied', 'hold', 'held', 'attach', 'link'],
	    disconnect: ['disconnect', 'leave open', 'float', 'do not connect'],
	    place: ['place', 'locate', 'position', 'route'],
	    configure: ['configure', 'set', 'select', 'program'],
	    limit: ['limit', 'keep', 'ensure', 'maintain', 'must', 'shall'],
	    avoid: ['avoid', 'prevent', 'never', 'must not', 'shall not', 'do not'],
	    use: ['use', 'provide', 'add', 'include'],
	};
	const VOLT = { canonical: 'v', declaration: sigma_engine_shared_1.CommonParameterUnits.voltage };
	const AMP = { canonical: 'a', declaration: sigma_engine_shared_1.CommonParameterUnits.current };
	const OHM = { canonical: 'ohm', declaration: sigma_engine_shared_1.CommonParameterUnits.resistance };
	const FARAD = { canonical: 'f', declaration: sigma_engine_shared_1.CommonParameterUnits.capacitance };
	const HERTZ = { canonical: 'hz', declaration: sigma_engine_shared_1.CommonParameterUnits.frequency };
	const SECOND = { canonical: 's', declaration: sigma_engine_shared_1.CommonParameterUnits.time };
	const SI_UNIT_SPELLINGS = {
	    v: VOLT,
	    mv: VOLT,
	    a: AMP,
	    ma: AMP,
	    ua: AMP,
	    µa: AMP,
	    μa: AMP,
	    ohm: OHM,
	    ω: OHM,
	    kohm: OHM,
	    kω: OHM,
	    mohm: OHM,
	    mω: OHM,
	    uf: FARAD,
	    µf: FARAD,
	    μf: FARAD,
	    nf: FARAD,
	    pf: FARAD,
	    hz: HERTZ,
	    khz: HERTZ,
	    mhz: HERTZ,
	    s: SECOND,
	    ms: SECOND,
	    us: SECOND,
	    µs: SECOND,
	    μs: SECOND,
	    ns: SECOND,
	    ps: SECOND,
	    fs: SECOND,
	};
	const NON_SI_UNIT_SCALE = {
	    '%': { canonical: 'pct', multiplier: 1 },
	    percent: { canonical: 'pct', multiplier: 1 },
	    degc: { canonical: 'degc', multiplier: 1 },
	    '°c': { canonical: 'degc', multiplier: 1 },
	    degf: { canonical: 'degf', multiplier: 1 },
	    '°f': { canonical: 'degf', multiplier: 1 },
	    k: { canonical: 'k', multiplier: 1 },
	    m: { canonical: 'm', multiplier: 1 },
	    cm: { canonical: 'm', multiplier: 1e-2 },
	    mm: { canonical: 'm', multiplier: 1e-3 },
	    mil: { canonical: 'm', multiplier: 2.54e-5 },
	    in: { canonical: 'm', multiplier: 0.0254 },
	    inch: { canonical: 'm', multiplier: 0.0254 },
	    inches: { canonical: 'm', multiplier: 0.0254 },
	    db: { canonical: 'db', multiplier: 1 },
	};
	const UNIT_PATTERN = [...Object.keys(SI_UNIT_SPELLINGS), ...Object.keys(NON_SI_UNIT_SCALE)]
	    .sort((a, b) => b.length - a.length)
	    .map((unit) => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
	    .join('|');
	const NUMBER_PATTERN = '-?(?:\\d+(?:\\.\\d+)?|\\.\\d+)(?:e[+-]?\\d+)?';
	const QUANTITY_PATTERN = new RegExp(`(${NUMBER_PATTERN})\\s*(${UNIT_PATTERN})(?![a-z0-9µω°%])`, 'gi');
	const BARE_TOLERANCE_PATTERN = new RegExp(`(?:±|\\+\\s*\\/\\s*-)\\s*(${NUMBER_PATTERN})\\s*(${UNIT_PATTERN})?`, 'gi');
	const ASYMMETRIC_TOLERANCE_PATTERN = new RegExp(`\\+\\s*(${NUMBER_PATTERN})\\s*\\/\\s*-\\s*(${NUMBER_PATTERN})\\s*(${UNIT_PATTERN})?`, 'gi');
	function subjectKey(subjectRaw, signals) {
	    const normalized = normalizeWords(subjectRaw);
	    const signal = earliestNamedSignal(normalized, signals);
	    if (signal !== undefined) {
	        return { kind: SubjectKind.Signal, ref: signalSubjectRef(signal) };
	    }
	    const pin = normalized.match(/\b(?:pin|ball|pad)\s*([a-z]?\d+)\b/i);
	    if (pin?.[1] !== undefined) {
	        return { kind: SubjectKind.Pin, ref: pin[1].toLowerCase() };
	    }
	    if (/\b(sequence|sequencing|power.?up|power.?down|startup|shutdown)\b/.test(normalized)) {
	        return { kind: SubjectKind.Sequencing, ref: 'power' };
	    }
	    if (/\b(vcc|vdd|vss|gnd|supply|rail|power)\b/.test(normalized)) {
	        return { kind: SubjectKind.Rail, ref: 'power' };
	    }
	    if (/\b(layout|pcb|routing|trace|placement)\b/.test(normalized)) {
	        return { kind: SubjectKind.Layout, ref: 'layout' };
	    }
	    if (/\b(thermal|temperature|heatsink|junction)\b/.test(normalized)) {
	        return { kind: SubjectKind.Thermal, ref: 'thermal' };
	    }
	    return { kind: SubjectKind.Device, ref: normalized || 'device' };
	}
	function signalSectionSlug(ref) {
	    const slugged = ref
	        .toLowerCase()
	        .replace(/[^a-z0-9]+/g, '-')
	        .replace(/^-+|-+$/g, '');
	    if (slugged !== '') {
	        return slugged;
	    }
	    const codePoints = [];
	    for (const character of ref) {
	        const code = character.codePointAt(0);
	        if (code !== undefined) {
	            codePoints.push(`u${code.toString(16)}`);
	        }
	    }
	    return [SIGNAL_SLUG_PREFIX, ...codePoints].join('-');
	}
	function sectionSlug(key) {
	    switch (key.kind) {
	        case SubjectKind.Signal:
	            return signalSectionSlug(key.ref);
	        case SubjectKind.Pin:
	            return `pin-${kebabFragment(key.ref)}`;
	        case SubjectKind.Rail:
	            return 'power';
	        case SubjectKind.Layout:
	            return 'layout';
	        case SubjectKind.Sequencing:
	            return 'power-sequencing';
	        case SubjectKind.Thermal:
	            return 'thermal';
	        case SubjectKind.Device:
	            return 'device';
	    }
	}
	function actionVerbClass(text) {
	    const normalized = normalizeWords(text);
	    const best = Object.entries(ACTION_VERBS)
	        .flatMap(([action, markers]) => markers.map((marker) => ({ action, marker: normalizeWords(marker) })))
	        .filter(({ marker }) => phraseOccurs(normalized, marker))
	        .sort((left, right) => right.marker.length - left.marker.length || left.action.localeCompare(right.action))[0];
	    return best?.action ?? normalized.split(' ')[0] ?? 'other';
	}
	function quantityFingerprint(text) {
	    return quantityTokens(text).join('|');
	}
	function textJaccard(left, right) {
	    const a = wordSet(normalizeWords(left));
	    const b = wordSet(normalizeWords(right));
	    const unionSize = new Set([...a, ...b]).size;
	    if (unionSize === 0) {
	        return 1;
	    }
	    let intersection = 0;
	    for (const token of a) {
	        if (b.has(token)) {
	            intersection += 1;
	        }
	    }
	    return intersection / unionSize;
	}
	ruleNormalization.jaccard = textJaccard;
	function normalizeWords(value) {
	    return value
	        .toLowerCase()
	        .replace(/[^a-z0-9µω]+/g, ' ')
	        .replace(/\s+/g, ' ')
	        .trim();
	}
	function normalizeCitationWhitespace(value) {
	    return value.replace(/\s+/g, ' ').trim();
	}
	function normalizedCitationIncludes(source, quote) {
	    const normalizedSource = normalizeCitationWhitespace(source);
	    const fragments = quote
	        .split(/\[(?:\.\.\.|…)\]/)
	        .map(normalizeCitationWhitespace)
	        .filter(Boolean);
	    let cursor = 0;
	    for (const fragment of fragments) {
	        const match = normalizedSource.indexOf(fragment, cursor);
	        if (match < 0) {
	            return false;
	        }
	        cursor = match + fragment.length;
	    }
	    return fragments.length > 0;
	}
	function quantityTokens(text) {
	    const quantities = [];
	    for (const match of text.matchAll(QUANTITY_PATTERN)) {
	        const normalized = normalizeQuantity(match[1], match[2]);
	        if (normalized !== null) {
	            quantities.push(normalized);
	        }
	    }
	    for (const match of text.matchAll(BARE_TOLERANCE_PATTERN)) {
	        if (match[2] === undefined) {
	            const numeric = Number(match[1]);
	            if (Number.isFinite(numeric)) {
	                quantities.push(`tol:${formatQuantity(numeric)}`);
	            }
	        }
	    }
	    for (const match of text.matchAll(ASYMMETRIC_TOLERANCE_PATTERN)) {
	        const [high, low, unit] = [match[1], match[2], match[3]];
	        if (high === undefined || low === undefined) {
	            continue;
	        }
	        const normalizedHigh = unit === undefined ? formatQuantity(Number(high)) : normalizeQuantity(high, unit);
	        const normalizedLow = unit === undefined ? formatQuantity(Number(low)) : normalizeQuantity(low, unit);
	        if (normalizedHigh !== null && normalizedLow !== null) {
	            quantities.push(`tol:+${normalizedHigh}/-${normalizedLow}`);
	        }
	    }
	    return [...new Set(quantities)].sort();
	}
	function kebabFragment(value) {
	    const slugged = value
	        .toLowerCase()
	        .replace(/[^a-z0-9]+/g, '-')
	        .replace(/^-|-$/g, '');
	    return slugged === '' ? 'device' : slugged;
	}
	function normalizeQuantity(rawNumeric, rawUnit) {
	    const numeric = Number(rawNumeric);
	    if (rawNumeric === undefined || rawUnit === undefined || !Number.isFinite(numeric)) {
	        return null;
	    }
	    const siUnit = SI_UNIT_SPELLINGS[rawUnit.toLowerCase()];
	    if (siUnit !== undefined) {
	        try {
	            const scaled = sigma_engine_shared_1.Utils.parseNumericalSIValue(`${rawNumeric}${rawUnit}`, siUnit.declaration);
	            return Number.isFinite(scaled) ? `${formatQuantity(scaled)}${siUnit.canonical}` : null;
	        }
	        catch {
	            return null;
	        }
	    }
	    const unit = NON_SI_UNIT_SCALE[rawUnit.toLowerCase()];
	    return unit === undefined ? null : `${formatQuantity(numeric * unit.multiplier)}${unit.canonical}`;
	}
	function signalSubjectRef(signal) {
	    const candidates = [signal.name, signal.id];
	    const worded = candidates.find((candidate) => normalizeWords(candidate) !== '');
	    return worded ?? candidates.find((candidate) => candidate.trim() !== '') ?? '';
	}
	function earliestNamedSignal(normalized, signals) {
	    const padded = ` ${normalized} `;
	    let best;
	    for (const signal of signals) {
	        for (const name of [signal.id, signal.name, ...(signal.aliases ?? [])].map(normalizeWords)) {
	            const position = name === '' ? -1 : padded.indexOf(` ${name} `);
	            if (position === -1) {
	                continue;
	            }
	            const closer = best === undefined || position < best.position;
	            const longerAtSamePosition = best !== undefined && position === best.position && name.length > best.length;
	            if (closer || longerAtSamePosition) {
	                best = { signal, position, length: name.length };
	            }
	        }
	    }
	    return best?.signal;
	}
	function wordSet(value) {
	    return new Set(value.split(' ').filter(Boolean));
	}
	function phraseOccurs(source, phrase) {
	    return ` ${source} `.includes(` ${phrase} `);
	}
	function formatQuantity(value) {
	    return Number(value.toPrecision(12)).toString();
	}
	
	return ruleNormalization;
}

var hasRequiredImplementationRules;

function requireImplementationRules () {
	if (hasRequiredImplementationRules) return ImplementationRules;
	hasRequiredImplementationRules = 1;
	(function (exports) {
		var __createBinding = (ImplementationRules && ImplementationRules.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ImplementationRules && ImplementationRules.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireCanonicalImplementationRules(), exports);
		__exportStar(requireDeriveRuleModality(), exports);
		__exportStar(requireImplementationRuleIds(), exports);
		__exportStar(requireImplementationRules$1(), exports);
		__exportStar(requireImplementationRulesHistoryEvent(), exports);
		__exportStar(requireNormativeSentenceDetector(), exports);
		__exportStar(requireRewriteRuleModality(), exports);
		__exportStar(requireRuleCitation(), exports);
		__exportStar(requireRuleNormalization(), exports);
		__exportStar(requireSentenceSplitter(), exports);
		
	} (ImplementationRules));
	return ImplementationRules;
}

var generationEvent = {};

var hasRequiredGenerationEvent;

function requireGenerationEvent () {
	if (hasRequiredGenerationEvent) return generationEvent;
	hasRequiredGenerationEvent = 1;
	Object.defineProperty(generationEvent, "__esModule", { value: true });
	generationEvent.GenerationEventSchema = generationEvent.GenerationEventKind = void 0;
	const z = /*@__PURE__*/ requireZod();
	const implementation_rules_history_event_1 = requireImplementationRulesHistoryEvent();
	var GenerationEventKind;
	(function (GenerationEventKind) {
	    GenerationEventKind["ImplementationRules"] = "implementation_rules";
	})(GenerationEventKind || (generationEvent.GenerationEventKind = GenerationEventKind = {}));
	generationEvent.GenerationEventSchema = z.discriminatedUnion('kind', [
	    z
	        .object({
	        kind: z.literal(GenerationEventKind.ImplementationRules),
	        event: implementation_rules_history_event_1.ImplementationRulesHistoryEventSchema,
	    })
	        .strict(),
	]);
	
	return generationEvent;
}

var Parse = {};

var Kicad = {};

var board$1 = {};

var NodeParser = {};

var Iterator = {};

var hasRequiredIterator;

function requireIterator () {
	if (hasRequiredIterator) return Iterator;
	hasRequiredIterator = 1;
	Object.defineProperty(Iterator, "__esModule", { value: true });
	Iterator.StatefulIterator = void 0;
	class StatefulIterator {
	    constructor(iterator) {
	        this.iterator = iterator;
	        this.currentResult = iterator.next();
	    }
	    current() {
	        return this.currentResult.value;
	    }
	    next() {
	        this.currentResult = this.iterator.next();
	        return !this.done;
	    }
	    get done() {
	        return Boolean(this.currentResult.done);
	    }
	    updateCurrent(value) {
	        this.currentResult.value = value;
	    }
	}
	Iterator.StatefulIterator = StatefulIterator;
	
	return Iterator;
}

var hasRequiredNodeParser;

function requireNodeParser () {
	if (hasRequiredNodeParser) return NodeParser;
	hasRequiredNodeParser = 1;
	Object.defineProperty(NodeParser, "__esModule", { value: true });
	NodeParser.SExpressionFileParser = void 0;
	const Iterator_1 = requireIterator();
	function parseNode(iterator) {
	    let word = iterator.current().trim();
	    if (!word.startsWith('(') || !word.length) {
	        throw new Error('Start of node does not match expected schema.');
	    }
	    if (word.endsWith(')')) {
	        iterator.updateCurrent(word.slice(0, -1));
	        return {
	            name: word.slice(1, -1),
	            values: [],
	            nodes: [],
	        };
	    }
	    const node = {
	        name: word.slice(1),
	        values: [],
	        nodes: [],
	    };
	    iterator.next();
	    do {
	        word = iterator.current().trim();
	        if (!word?.length) {
	            continue;
	        }
	        else if (word.startsWith('(')) {
	            node.nodes.push(parseNode(iterator));
	        }
	        else if (word.startsWith('"')) {
	            node.values.push(parseNodeText(iterator));
	        }
	        else {
	            const stripped = word.replace(/\)/g, '');
	            if (stripped.length) {
	                node.values.push(stripped);
	            }
	        }
	        word = iterator.current().trim();
	        if (word.endsWith(')')) {
	            iterator.updateCurrent(word.slice(0, -1));
	            return node;
	        }
	    } while (iterator.next());
	    return node;
	}
	function parseNodeText(iterator) {
	    const textEndMatch = /([\s\S]*")\)*$/g;
	    let word = iterator.current().trim();
	    if (!word.startsWith('"')) {
	        throw new Error('Start of text object does not match expected schema.');
	    }
	    const match = textEndMatch.exec(word);
	    if (match && match.length > 1) {
	        return match[1];
	    }
	    let str = word;
	    while (iterator.next()) {
	        word = iterator.current().trim();
	        const match = textEndMatch.exec(word);
	        if (match && match.length > 1) {
	            return str + ` ${match[1]}`;
	        }
	        str += ` ${word}`;
	    }
	    return str;
	}
	class SExpressionFileParser {
	    constructor() {
	        this.cache = new Map();
	    }
	    parseFile(filename, contents) {
	        if (!this.cache.has(filename)) {
	            this.cache.set(filename, SExpressionFileParser.Parse(contents));
	        }
	        return this.cache.get(filename);
	    }
	    static Parse(contents) {
	        return parseNode(SExpressionFileParser.GetContentsIterator(contents));
	    }
	    static GetContentsIterator(contents) {
	        return new Iterator_1.StatefulIterator(SExpressionFileParser.GetFileIterator(contents));
	    }
	    static *GetFileIterator(contents) {
	        let wordStart = 0;
	        let inQuotes = false;
	        let i = 0;
	        for (; i < contents.length; ++i) {
	            const currentChar = contents[i];
	            if (currentChar === '"') {
	                if (inQuotes) {
	                    let j = 0;
	                    while (j < i && contents[i - j - 1] === '\\') {
	                        j++;
	                    }
	                    const escapedQuote = j % 2 !== 0;
	                    if (i > 0 && escapedQuote) {
	                        continue;
	                    }
	                    yield contents.substring(wordStart, i + 1);
	                    inQuotes = false;
	                    wordStart = i + 1;
	                    continue;
	                }
	                else {
	                    inQuotes = true;
	                    continue;
	                }
	            }
	            if (inQuotes) {
	                continue;
	            }
	            if (/\s/.test(currentChar)) {
	                if (i === wordStart) {
	                    wordStart = i + 1;
	                    continue;
	                }
	                yield contents.substring(wordStart, i);
	                wordStart = i + 1;
	                continue;
	            }
	        }
	        if (i > wordStart) {
	            yield contents.substring(wordStart);
	        }
	    }
	}
	NodeParser.SExpressionFileParser = SExpressionFileParser;
	
	return NodeParser;
}

var layers$2 = {};

var hasRequiredLayers$2;

function requireLayers$2 () {
	if (hasRequiredLayers$2) return layers$2;
	hasRequiredLayers$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.KicadSchLayerMap = exports.PcbLayerMap = exports.KicadPcbLayerSelector = exports.KicadSchLayerSelector = exports.KICAD_SCH_LAYERSET = exports.KICAD_BRD_LAYERSET = void 0;
		exports.getKicadDefaultLayerSets = getKicadDefaultLayerSets;
		exports.getKicadSchLayerByColorType = getKicadSchLayerByColorType;
		exports.getKicadSchLayerByType = getKicadSchLayerByType;
		exports.getKicadSchLayers = getKicadSchLayers;
		const sigma_engine_shared_1 = requireDist$1();
		const { toArray } = sigma_engine_shared_1.Utils;
		const { PcbLayerType, SchLayerType } = sigma_engine_shared_1.DesignModels;
		exports.KICAD_BRD_LAYERSET = 'kicadbrd';
		exports.KICAD_SCH_LAYERSET = 'kicadsch';
		class KicadLayerSelector {
		    filterLayers(name) {
		        return this.getLayersByName(name);
		    }
		    getLayerByColorType(colorType, defaultType) {
		        switch (colorType) {
		            case 'default':
		                return this.getLayerByType(defaultType);
		            case 'none':
		                return undefined;
		            case 'background':
		                return this.getLayerByType(sigma_engine_shared_1.DesignModels.SchLayerType.Background);
		        }
		        return this.getLayerByType(defaultType);
		    }
		    getLayerByType(layer) {
		        const layers = this.getLayersByType(layer);
		        if (layers) {
		            return layers[0];
		        }
		        return this.getLayerByType(sigma_engine_shared_1.DesignModels.UnknownLayerType.Other);
		    }
		    getLayers() {
		        return Array.from(this.enumerateLayers());
		    }
		}
		class KicadSchLayerSelector extends KicadLayerSelector {
		    getLayersByName(name) {
		        return toArray(Array.from(exports.KicadSchLayerMap.values()).find((l) => l.name === name));
		    }
		    getLayersByType(type) {
		        return toArray(exports.KicadSchLayerMap.get(type));
		    }
		    enumerateLayers() {
		        return exports.KicadSchLayerMap.values();
		    }
		}
		exports.KicadSchLayerSelector = KicadSchLayerSelector;
		class KicadPcbLayerSelector extends KicadLayerSelector {
		    mapLayerDetailsToLayer(details) {
		        return { ...details, name: details.name[0] };
		    }
		    getLayersByName(value) {
		        let [name, attribute] = value.split('.');
		        if (!name) {
		            return [];
		        }
		        if (!attribute) {
		            attribute = '';
		        }
		        return exports.PcbLayerMap.filter((l) => (name === '*' || l.name.includes(name)) && l.attribute === attribute).map((l) => this.mapLayerDetailsToLayer(l));
		    }
		    getLayersByType(type) {
		        return exports.PcbLayerMap.filter((layer) => layer.type === type).map((l) => this.mapLayerDetailsToLayer(l));
		    }
		    *enumerateLayers() {
		        for (const layer of exports.PcbLayerMap) {
		            yield this.mapLayerDetailsToLayer(layer);
		        }
		    }
		}
		exports.KicadPcbLayerSelector = KicadPcbLayerSelector;
		function getKicadDefaultLayerSets() {
		    return {
		        [exports.KICAD_SCH_LAYERSET]: {
		            type: 'layerSet',
		            layerSetType: 'schematic',
		            id: exports.KICAD_SCH_LAYERSET,
		            layers: new KicadSchLayerSelector().getLayers(),
		        },
		        [exports.KICAD_BRD_LAYERSET]: {
		            type: 'layerSet',
		            layerSetType: 'pcb',
		            id: exports.KICAD_BRD_LAYERSET,
		            layers: new KicadPcbLayerSelector().getLayers(),
		        },
		    };
		}
		function getKicadSchLayerByColorType(colorType, defaultType) {
		    switch (colorType) {
		        case 'default':
		            return getKicadSchLayerByType(defaultType);
		        case 'none':
		            return undefined;
		        case 'background':
		            return getKicadSchLayerByType(SchLayerType.Background);
		    }
		    return getKicadSchLayerByType(defaultType);
		}
		function getKicadSchLayerByType(layer) {
		    return exports.KicadSchLayerMap.get(layer);
		}
		function getKicadSchLayers() {
		    return Array.from(exports.KicadSchLayerMap.values());
		}
		exports.PcbLayerMap = [
		    { id: 0, name: ['F'], attribute: 'Cu', type: PcbLayerType.F, face: 'F', faceMirrorLayer: 31 },
		    { id: 1, name: ['In1', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 2, name: ['In2', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 3, name: ['In3', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 4, name: ['In4', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 5, name: ['In5', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 6, name: ['In6', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 7, name: ['In7', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 8, name: ['In8', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 9, name: ['In9', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 10, name: ['In10', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 11, name: ['In11', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 12, name: ['In12', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 13, name: ['In13', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 14, name: ['In14', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 15, name: ['In15', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 16, name: ['In16', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 17, name: ['In17', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 18, name: ['In18', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 19, name: ['In19', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 20, name: ['In20', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 21, name: ['In21', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 22, name: ['In22', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 23, name: ['In23', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 24, name: ['In24', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 25, name: ['In25', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 26, name: ['In26', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 27, name: ['In27', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 28, name: ['In28', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 29, name: ['In29', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 30, name: ['In30', 'Inner'], attribute: 'Cu', type: PcbLayerType.Inner },
		    { id: 31, name: ['B'], attribute: 'Cu', type: PcbLayerType.B, face: 'B', faceMirrorLayer: 0 },
		    { id: 32, name: ['B'], attribute: 'Adhes', type: PcbLayerType.Other, face: 'B', faceMirrorLayer: 33 },
		    { id: 33, name: ['F'], attribute: 'Adhes', type: PcbLayerType.Other, face: 'F', faceMirrorLayer: 32 },
		    { id: 34, name: ['B'], attribute: 'Paste', type: PcbLayerType.Paste, face: 'B', faceMirrorLayer: 35 },
		    { id: 35, name: ['F'], attribute: 'Paste', type: PcbLayerType.Paste, face: 'F', faceMirrorLayer: 34 },
		    { id: 36, name: ['B'], attribute: 'SilkS', type: PcbLayerType.SilkS, face: 'B', faceMirrorLayer: 37 },
		    { id: 37, name: ['F'], attribute: 'SilkS', type: PcbLayerType.SilkS, face: 'F', faceMirrorLayer: 36 },
		    { id: 38, name: ['B'], attribute: 'Mask', type: PcbLayerType.Mask, face: 'B', faceMirrorLayer: 39 },
		    { id: 39, name: ['F'], attribute: 'Mask', type: PcbLayerType.Mask, face: 'F', faceMirrorLayer: 38 },
		    { id: 40, name: ['Dwgs'], attribute: 'User', type: PcbLayerType.Other },
		    { id: 41, name: ['Cmts'], attribute: 'User', type: PcbLayerType.Other },
		    { id: 42, name: ['Eco1'], attribute: 'User', type: PcbLayerType.Other },
		    { id: 43, name: ['Eco2'], attribute: 'User', type: PcbLayerType.Other },
		    { id: 44, name: ['Edge'], attribute: 'Cuts', type: PcbLayerType.Edge },
		    { id: 45, name: ['Margin'], attribute: '', type: PcbLayerType.Other },
		    { id: 46, name: ['B'], attribute: 'CrtYd', type: PcbLayerType.Other, face: 'B', faceMirrorLayer: 47 },
		    { id: 47, name: ['F'], attribute: 'CrtYd', type: PcbLayerType.Other, face: 'F', faceMirrorLayer: 46 },
		    { id: 48, name: ['B'], attribute: 'Fab', type: PcbLayerType.Other, face: 'B', faceMirrorLayer: 49 },
		    { id: 49, name: ['F'], attribute: 'Fab', type: PcbLayerType.Other, face: 'F', faceMirrorLayer: 48 },
		    { id: 50, name: ['User'], attribute: '1', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 51, name: ['User'], attribute: '2', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 52, name: ['User'], attribute: '3', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 53, name: ['User'], attribute: '4', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 54, name: ['User'], attribute: '5', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 55, name: ['User'], attribute: '6', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 56, name: ['User'], attribute: '7', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 57, name: ['User'], attribute: '8', type: PcbLayerType.Other, defaultVisible: false },
		    { id: 58, name: ['User'], attribute: '9', type: PcbLayerType.Other, defaultVisible: false },
		];
		exports.KicadSchLayerMap = new Map([
		    [SchLayerType.Label, { id: 1, type: SchLayerType.Label, name: 'Label' }],
		    [SchLayerType.NameLabel, { id: 2, type: SchLayerType.NameLabel, name: 'NameLabel' }],
		    [SchLayerType.ValueLabel, { id: 3, type: SchLayerType.ValueLabel, name: 'ValueLabel' }],
		    [SchLayerType.Pin, { id: 4, type: SchLayerType.Pin, name: 'Pin' }],
		    [SchLayerType.PinName, { id: 5, type: SchLayerType.PinName, name: 'PinName' }],
		    [SchLayerType.PinNumber, { id: 6, type: SchLayerType.PinNumber, name: 'PinNumber' }],
		    [SchLayerType.Symbol, { id: 7, type: SchLayerType.Symbol, name: 'Symbol' }],
		    [SchLayerType.Wire, { id: 8, type: SchLayerType.Wire, name: 'Wire' }],
		    [SchLayerType.Bus, { id: 9, type: SchLayerType.Bus, name: 'Bus' }],
		    [SchLayerType.Junction, { id: 10, type: SchLayerType.Junction, name: 'Junction' }],
		    [SchLayerType.BusJunction, { id: 11, type: SchLayerType.BusJunction, name: 'BusJunction' }],
		    [SchLayerType.Other, { id: 12, type: SchLayerType.Other, name: 'Other' }],
		    [SchLayerType.Background, { id: 13, type: SchLayerType.Background, name: 'Background Contrast' }],
		    [SchLayerType.Image, { id: 14, type: SchLayerType.Image, name: 'Image' }],
		]);
		
	} (layers$2));
	return layers$2;
}

var Area = {};

var hasRequiredArea;

function requireArea () {
	if (hasRequiredArea) return Area;
	hasRequiredArea = 1;
	Object.defineProperty(Area, "__esModule", { value: true });
	const Transform_1 = requireTransform();
	class Area$1 {
	    constructor() {
	        this.xMin = Number.POSITIVE_INFINITY;
	        this.yMin = Number.POSITIVE_INFINITY;
	        this.xMax = Number.NEGATIVE_INFINITY;
	        this.yMax = Number.NEGATIVE_INFINITY;
	    }
	    toRectangle() {
	        return {
	            x1: this.xMin,
	            x2: this.xMax,
	            y1: this.yMin,
	            y2: this.yMax,
	        };
	    }
	    toModelBoundingBox(fallback = { minx: -100, miny: -100, maxx: 100, maxy: 100 }) {
	        return {
	            minx: Number.isFinite(this.xMin) ? this.xMin : fallback.minx,
	            miny: Number.isFinite(this.yMin) ? this.yMin : fallback.miny,
	            maxx: Number.isFinite(this.xMax) ? this.xMax : fallback.maxx,
	            maxy: Number.isFinite(this.yMax) ? this.yMax : fallback.maxy,
	        };
	    }
	    extend(v) {
	        if (typeof v[0] === 'number' && typeof v[1] === 'number') {
	            this.xMin = Math.min(this.xMin, v[0]);
	            this.yMin = Math.min(this.yMin, v[1]);
	            this.xMax = Math.max(this.xMax, v[0]);
	            this.yMax = Math.max(this.yMax, v[1]);
	        }
	    }
	    extendWithDrawing(core, drawing, warnings) {
	        switch (drawing.type) {
	            case 'track':
	                this.extend(drawing.start);
	                this.extend(drawing.end);
	                break;
	            case 'segment':
	            case 'bus':
	                this.extend(drawing.start);
	                this.extend(drawing.end);
	                break;
	            case 'circle':
	                this.extend(drawing.center.map((v) => v - drawing.radius));
	                this.extend(drawing.center.map((v) => v + drawing.radius));
	                break;
	            case 'zone':
	                drawing.polygons.forEach((z) => this.extendWithVertices(z.points));
	                break;
	            case 'via':
	                this.extend(drawing.center.map((v) => v - drawing.diameter / 2));
	                this.extend(drawing.center.map((v) => v + drawing.diameter / 2));
	                break;
	            case 'polygon':
	                this.extendWithVertices(drawing.points);
	                break;
	            case 'text':
	                this.extend(drawing.pos);
	                if (drawing.bounds) {
	                    this.extend((0, Transform_1.translatePoint)(drawing.pos, drawing.bounds));
	                }
	                break;
	            case 'label':
	                this.extend(drawing.pos);
	                break;
	            case 'junction':
	                this.extend(drawing.position);
	                break;
	            case 'rect':
	            case 'pin':
	                this.extend(drawing.start);
	                this.extend(drawing.end);
	                break;
	            case 'pad':
	                this.extend((0, Transform_1.translatePoint)(drawing.pos, [-drawing.diameter[0] / 2, -drawing.diameter[1] / 2]));
	                this.extend((0, Transform_1.translatePoint)(drawing.pos, [drawing.diameter[0] / 2, drawing.diameter[1] / 2]));
	                break;
	            case 'group': {
	                for (const group of drawing.drawings) {
	                    this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(group, {
	                        pos: drawing.pos,
	                        transform: {
	                            angle: drawing.angle,
	                        },
	                    }), warnings);
	                }
	                break;
	            }
	            case 'arc': {
	                this.extend((0, Transform_1.translatePoint)(drawing.center, [drawing.radius, drawing.radius]));
	                this.extend((0, Transform_1.translatePoint)(drawing.center, [drawing.radius, -drawing.radius]));
	                this.extend((0, Transform_1.translatePoint)(drawing.center, [-drawing.radius, drawing.radius]));
	                this.extend((0, Transform_1.translatePoint)(drawing.center, [-drawing.radius, -drawing.radius]));
	                break;
	            }
	            case 'schref':
	                if (core) {
	                    this.extendFromModule(core, drawing.drawModule, warnings);
	                }
	                else {
	                    warnings.push(`Area extendWithDrawing did not have project to extend schref`);
	                }
	                break;
	            case 'image':
	                this.extend(drawing.pos);
	                this.extend([drawing.pos[0] + drawing.width, drawing.pos[1] + drawing.height]);
	                break;
	            default:
	                warnings.push(`Area extendWithDrawing does not support ${drawing.type}`);
	                break;
	        }
	    }
	    extendWithVertices(vertices) {
	        vertices.forEach((v) => {
	            this.extend(v);
	        });
	    }
	    extendFromSchModel(core, model, projectLayers, warnings, includeHidden = true) {
	        for (const [key, drawings] of Object.entries(model.drawings)) {
	            if (!includeHidden) {
	                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == key);
	                if (!layerInfo || layerInfo?.defaultVisible === false) {
	                    return this;
	                }
	            }
	            for (const drawing of drawings) {
	                this.extendWithDrawing(core, drawing, warnings);
	            }
	        }
	        model.modules.forEach((module) => {
	            this.extendFromModule(core, module, warnings);
	        });
	        return this;
	    }
	    extendFromModule(core, module, warnings) {
	        const component = core.components[module.componentRef];
	        if (!component) {
	            throw new Error(`Could not find component with ref '${module.componentRef}'.`);
	        }
	        component.footprints.forEach((footprint) => {
	            if (footprint.ref !== module.footprintRef) {
	                return;
	            }
	            footprint.drawings.forEach((d) => {
	                if (d.drawing.type === 'text') {
	                    return;
	                }
	                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
	            });
	            footprint.pads.forEach((p) => {
	                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(p, module), warnings);
	            });
	        });
	        component.symbols.forEach((symbol) => {
	            if (symbol.ref !== module.footprintRef) {
	                return;
	            }
	            symbol.drawings.forEach((d) => {
	                if (d.drawing.type === 'text') {
	                    return;
	                }
	                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
	            });
	            symbol.pins.forEach((pin) => {
	                pin.drawings?.forEach((d) => {
	                    if (d.drawing.type === 'text') {
	                        return;
	                    }
	                    this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
	                });
	                this.extendWithDrawing(core, (0, Transform_1.transformDrawingByParent)(pin, module), warnings);
	            });
	        });
	    }
	    extendFromPcbModel(project, model, projectLayers, warnings, includeHidden = true) {
	        Object.keys(model.tracks).forEach((layer) => {
	            if (!includeHidden) {
	                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == layer);
	                if (!layerInfo || layerInfo.defaultVisible === false) {
	                    return;
	                }
	            }
	            model.tracks[layer].forEach((track) => {
	                this.extend(track.start);
	                this.extend(track.end);
	            });
	        });
	        Object.keys(model.zones).forEach((layer) => {
	            if (!includeHidden) {
	                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == layer);
	                if (!layerInfo || layerInfo?.defaultVisible === false) {
	                    return;
	                }
	            }
	            model.zones[layer].forEach((zone) => {
	                const drawing = (0, Transform_1.toDrawing)(zone);
	                if (drawing) {
	                    this.extendWithDrawing(project, drawing, warnings);
	                }
	            });
	        });
	        model.vias.forEach((via) => {
	            const drawing = (0, Transform_1.toDrawing)(via);
	            if (drawing) {
	                this.extendWithDrawing(project, drawing, warnings);
	            }
	        });
	        model.modules.forEach((module) => {
	            const footprint = project.components[module.componentRef].footprints.find((f) => f.name === module.footprintRef);
	            footprint.drawings.forEach((d) => {
	                this.extendWithDrawing(project, (0, Transform_1.transformDrawingByParent)(d.drawing, module), warnings);
	            });
	        });
	        model.edges.forEach((edge) => {
	            this.extendWithDrawing(project, edge, warnings);
	        });
	        for (const [key, drawings] of Object.entries(model.drawings)) {
	            if (!includeHidden) {
	                const layerInfo = projectLayers.find((projectLayer) => projectLayer.id.toString() == key);
	                if (!layerInfo || layerInfo?.defaultVisible === false) {
	                    return;
	                }
	            }
	            for (const drawing of drawings) {
	                this.extendWithDrawing(project, drawing, warnings);
	            }
	        }
	    }
	    extendWithDimensions(x, y, width, height) {
	        this.xMin = Math.min(this.xMin, x);
	        this.yMin = Math.min(this.yMin, y);
	        this.xMax = Math.max(this.xMax, x + width);
	        this.yMax = Math.max(this.yMax, y + height);
	    }
	    extendWithArea(a) {
	        this.xMin = Math.min(this.xMin, a.xMin);
	        this.yMin = Math.min(this.yMin, a.yMin);
	        this.xMax = Math.max(this.xMax, a.xMax);
	        this.yMax = Math.max(this.yMax, a.yMax);
	    }
	}
	Area.default = Area$1;
	
	return Area;
}

var v6ParseRectangle = {};

var v6Common = {};

var hasRequiredV6Common;

function requireV6Common () {
	if (hasRequiredV6Common) return v6Common;
	hasRequiredV6Common = 1;
	Object.defineProperty(v6Common, "__esModule", { value: true });
	v6Common.DefaultBrdGrid = v6Common.DefaultSchGrid = void 0;
	v6Common.parseKicadLineType = parseKicadLineType;
	v6Common.parseStrokeDirective = parseStrokeDirective;
	v6Common.parseFillDirective = parseFillDirective;
	v6Common.DefaultSchGrid = {
	    distance: 1.27,
	    units: 'mm',
	    invertY: true,
	};
	v6Common.DefaultBrdGrid = {
	    distance: 1.27,
	    units: 'mm',
	    invertY: true,
	};
	function parseKicadLineType(lineTypeStr) {
	    switch (lineTypeStr) {
	        case 'dash':
	        case 'dot':
	            return lineTypeStr;
	        case 'dash_dot':
	            return 'dash-dot';
	        case 'solid':
	        default:
	            return 'solid';
	    }
	}
	function parseStrokeDirective(n) {
	    const stroke = {
	        width: 0,
	        type: 'solid',
	    };
	    const widthNode = n.nodes.find((v) => v.name === 'width');
	    if (widthNode) {
	        stroke.width = parseFloat(widthNode.values[0]);
	    }
	    const typeNode = n.nodes.find((v) => v.name === 'type');
	    if (typeNode) {
	        stroke.type = parseKicadLineType(typeNode.values[0]);
	    }
	    return stroke;
	}
	function parseFillDirective(n, layerSelector, defaultLayer) {
	    const typeNode = n.nodes.find((v) => v.name === 'type');
	    let filled;
	    let fillLayer;
	    let colorOverride;
	    if (typeNode) {
	        filled = typeNode.values[0] === 'none' ? undefined : 1;
	        fillLayer = layerSelector.getLayerByColorType(typeNode.values[0], defaultLayer);
	        if (typeNode.values[0] === 'color') {
	            const colorNode = n.nodes.find((v) => v.name === 'color');
	            if (colorNode) {
	                colorOverride = [
	                    parseInt(colorNode.values[0]),
	                    parseInt(colorNode.values[1]),
	                    parseInt(colorNode.values[2]),
	                    parseInt(colorNode.values[3]),
	                ];
	            }
	        }
	    }
	    else if (n.values.length > 0) {
	        filled = n.values[0] === 'none' ? undefined : 1;
	        fillLayer = layerSelector.getLayerByColorType(n.values[0], defaultLayer);
	    }
	    return {
	        filled,
	        layer: fillLayer || layerSelector.getLayerByType(defaultLayer),
	        colorOverride,
	    };
	}
	
	return v6Common;
}

var hasRequiredV6ParseRectangle;

function requireV6ParseRectangle () {
	if (hasRequiredV6ParseRectangle) return v6ParseRectangle;
	hasRequiredV6ParseRectangle = 1;
	Object.defineProperty(v6ParseRectangle, "__esModule", { value: true });
	v6ParseRectangle.parseRectangle = parseRectangle;
	const Util_1 = requireUtil();
	const v6Common_1 = requireV6Common();
	const Utils_1 = requireUtils$4();
	function parseRectangle(node, isModule, warnings, defaultLayer, layerSelector, minStrokeWidth = 0) {
	    let start = [0, 0];
	    let end = [0, 0];
	    let strokeWidth = 0;
	    let filled;
	    let strokeLayer = defaultLayer && layerSelector.getLayerByType(defaultLayer);
	    let fillLayer = defaultLayer && layerSelector.getLayerByType(defaultLayer);
	    let colorOverride;
	    const flipY = isModule ? -1 : 1;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'start': {
	                start = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
	                break;
	            }
	            case 'end': {
	                end = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
	                break;
	            }
	            case 'fill': {
	                const fill = (0, v6Common_1.parseFillDirective)(n, layerSelector, defaultLayer);
	                filled = fill.filled;
	                fillLayer = fill.layer;
	                colorOverride = fill.colorOverride;
	                break;
	            }
	            case 'stroke': {
	                const widthNode = n.nodes.find((v) => v.name === 'width');
	                if (widthNode) {
	                    strokeWidth = parseFloat(widthNode.values[0]);
	                }
	                const typeNode = n.nodes.find((v) => v.name === 'type');
	                if (typeNode) {
	                    strokeLayer = layerSelector.getLayerByColorType(typeNode.values[0], defaultLayer);
	                }
	                break;
	            }
	            case 'layer': {
	                if (n.values.length === 1) {
	                    strokeLayer = layerSelector.filterLayers((0, Util_1.removeQuotes)(n.values[0]))[0];
	                }
	                if (n.values.length !== 1 || !strokeLayer) {
	                    warnings.push(`Unknow layer configuration for ${node.name}`);
	                    if (!strokeLayer) {
	                        strokeLayer = defaultLayer && layerSelector.getLayerByType(defaultLayer);
	                    }
	                }
	                break;
	            }
	        }
	    });
	    const strokeRect = {
	        type: 'rect',
	        start,
	        end,
	        strokeWidth: (0, Utils_1.max)(strokeWidth, minStrokeWidth),
	        filled: filled && strokeLayer?.id === fillLayer?.id ? true : undefined,
	        colorOverride,
	    };
	    const shapes = [];
	    if (filled && fillLayer && fillLayer.id !== strokeLayer?.id) {
	        const fillRect = {
	            type: 'rect',
	            start,
	            end,
	            strokeWidth: 0,
	            filled: true,
	        };
	        shapes.push({
	            layers: [fillLayer.id],
	            drawing: fillRect,
	        });
	    }
	    if (strokeLayer) {
	        shapes.push({
	            layers: [strokeLayer.id],
	            drawing: strokeRect,
	        });
	    }
	    return shapes;
	}
	
	return v6ParseRectangle;
}

var v6ParseArc = {};

var hasRequiredV6ParseArc;

function requireV6ParseArc () {
	if (hasRequiredV6ParseArc) return v6ParseArc;
	hasRequiredV6ParseArc = 1;
	Object.defineProperty(v6ParseArc, "__esModule", { value: true });
	v6ParseArc.parseArc = parseArc;
	const Util_1 = requireUtil();
	const v6Common_1 = requireV6Common();
	const Utils_1 = requireUtils$4();
	const Transform_1 = requireTransform();
	function parseArc(isModule, node, _isFootprint, warnings, defaultLayerName, layerSelector, minLineWidth = 0, netMap = []) {
	    let start = [0, 0];
	    let mid;
	    let end = [0, 0];
	    let lineWidth = 0;
	    let lineStyle;
	    let angleDeg;
	    let layerName = defaultLayerName;
	    let net;
	    const flipY = isModule ? -1 : 1;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'start': {
	                start = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
	                break;
	            }
	            case 'mid': {
	                mid = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
	                break;
	            }
	            case 'end': {
	                end = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
	                break;
	            }
	            case 'width': {
	                lineWidth = parseFloat(n.values[0]);
	                break;
	            }
	            case 'angle': {
	                angleDeg = parseFloat(n.values[0]);
	                break;
	            }
	            case 'layer':
	                layerName = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            case 'stroke': {
	                const stroke = (0, v6Common_1.parseStrokeDirective)(n);
	                lineWidth = stroke.width;
	                lineStyle = stroke.type;
	                break;
	            }
	            case 'net': {
	                const netId = n.values[0];
	                const netName = netMap.find((n) => n.id === netId);
	                if (netName) {
	                    net = netName.value;
	                }
	            }
	        }
	    });
	    if (!layerName) {
	        warnings.push(`Arc at [${start[0]}, ${start[1]}] => [${end[0]}, ${end[1]}] did not have layer specified`);
	        return undefined;
	    }
	    const layer = layerSelector.filterLayers(layerName)[0];
	    if (!layer) {
	        warnings.push(`Arc at [${start[0]}, ${start[1]}] => [${end[0]}, ${end[1]}] used unknown layer ${layerName}`);
	        return undefined;
	    }
	    let radius, center;
	    if (typeof angleDeg === 'number') {
	        const angleRad = (0, Utils_1.degreesToRadians)(angleDeg);
	        radius = Math.sqrt(Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2));
	        center = [
	            start[0],
	            start[1],
	        ];
	        if (center[0] <= end[0] && center[1] > end[1]) {
	            start = [center[0] + radius * Math.sin(angleRad), center[1] + radius * Math.cos(angleRad)];
	        }
	        else if (center[0] < end[0] && center[1] <= end[1]) {
	            start = [center[0] - radius * Math.cos(angleRad), center[1] + radius * Math.sin(angleRad)];
	        }
	        else if (center[0] >= end[0] - Number.EPSILON && center[1] < end[1]) {
	            start = [center[0] - radius * Math.sin(angleRad), center[1] + radius * Math.cos(angleRad)];
	        }
	        else {
	            start = [center[0] - radius * Math.cos(angleRad), center[1] - radius * Math.sin(angleRad)];
	        }
	    }
	    else if (mid) {
	        const found = findCircleFromPoints(start, mid, end);
	        radius = found.radius;
	        center = found.center;
	    }
	    else {
	        warnings.push(`Unable to compute arc at [${start[0]}, ${start[1]}] => [${end[0]}, ${end[1]}]`);
	        return undefined;
	    }
	    let startAngle = angleOfPoint(start, center, radius);
	    let endAngle = angleOfPoint(end, center, radius);
	    if (typeof angleDeg !== 'undefined') {
	        if (angleDeg >= 0) {
	            startAngle = endAngle - angleDeg;
	        }
	        else {
	            const temp = startAngle;
	            startAngle = endAngle;
	            endAngle = temp;
	        }
	    }
	    else if (endAngle > startAngle) {
	        if (mid) {
	            const midAngle = angleOfPoint(mid, center, radius);
	            if (midAngle < startAngle) {
	                const temp = startAngle;
	                startAngle = endAngle;
	                endAngle = temp;
	            }
	            else {
	                startAngle = (0, Transform_1.clampRotation)(startAngle + 360);
	            }
	        }
	        else {
	            const temp = startAngle;
	            startAngle = endAngle;
	            endAngle = temp;
	        }
	    }
	    const arc = {
	        type: 'arc',
	        center: [(0, Transform_1.roundToMaxOutputPositionPrecision)(center[0]), (0, Transform_1.roundToMaxOutputPositionPrecision)(center[1])],
	        startAngle: (0, Transform_1.roundToMaxOutputAnglePrecision)(360 - startAngle),
	        endAngle: (0, Transform_1.roundToMaxOutputAnglePrecision)(360 - endAngle),
	        radius: (0, Transform_1.roundToMaxOutputPositionPrecision)(radius),
	        lineWidth: (0, Utils_1.max)(lineWidth, minLineWidth),
	        net,
	    };
	    if (mid) {
	        const midAngle = angleOfPoint(mid, center, radius);
	        const midAngleOut = 360 - midAngle;
	        const normalize = (angle) => ((angle % 360) + 360) % 360;
	        const span = normalize(arc.endAngle - arc.startAngle);
	        const midOffset = normalize(midAngleOut - arc.startAngle);
	        if (midOffset > span) {
	            const temp = arc.startAngle;
	            arc.startAngle = arc.endAngle;
	            arc.endAngle = temp;
	        }
	    }
	    if (arc.endAngle === 0) {
	        arc.endAngle += 360;
	    }
	    if (lineStyle) {
	        arc.lineStyle = lineStyle;
	    }
	    return {
	        layers: [layer.id],
	        drawing: arc,
	    };
	}
	function angleOfPoint(point, center, radius) {
	    const cosine = Math.min(1, Math.max(-1, (0, Transform_1.roundToMultiple)((point[0] - center[0]) / radius, 0.0001)));
	    let angle = (0, Utils_1.radiansToDegrees)(Math.acos(cosine));
	    if (point[1] - center[1] > Number.EPSILON) {
	        angle = 360 - angle;
	    }
	    else if (Number.isNaN(angle)) {
	        angle = 0;
	    }
	    return angle;
	}
	function findCircleFromPoints(start, mid, end) {
	    const x12 = start[0] - mid[0];
	    const x13 = start[0] - end[0];
	    const y12 = start[1] - mid[1];
	    const y13 = start[1] - end[1];
	    const y31 = end[1] - start[1];
	    const y21 = mid[1] - start[1];
	    const x31 = end[0] - start[0];
	    const x21 = mid[0] - start[0];
	    const sx13 = Math.pow(start[0], 2) - Math.pow(end[0], 2);
	    const sy13 = Math.pow(start[1], 2) - Math.pow(end[1], 2);
	    const sx21 = Math.pow(mid[0], 2) - Math.pow(start[0], 2);
	    const sy21 = Math.pow(mid[1], 2) - Math.pow(start[1], 2);
	    const f = (sx13 * x12 + sy13 * x12 + sx21 * x13 + sy21 * x13) / (2 * (y31 * x12 - y21 * x13));
	    const g = (sx13 * y12 + sy13 * y12 + sx21 * y13 + sy21 * y13) / (2 * (x31 * y12 - x21 * y13));
	    const c = -Math.pow(start[0], 2) - Math.pow(start[1], 2) - 2 * g * start[0] - 2 * f * start[1];
	    const h = -g;
	    const k = -f;
	    const sqr_of_r = h * h + k * k - c;
	    const r = Math.sqrt(sqr_of_r);
	    return {
	        center: [(0, Transform_1.roundToMaxOutputPositionPrecision)(h), (0, Transform_1.roundToMaxOutputPositionPrecision)(k)],
	        radius: (0, Transform_1.roundToMaxOutputPositionPrecision)(r),
	    };
	}
	
	return v6ParseArc;
}

var v6ParseLabel = {};

var getMirrorValuesToKeepTextRightSideUp = {};

var hasRequiredGetMirrorValuesToKeepTextRightSideUp;

function requireGetMirrorValuesToKeepTextRightSideUp () {
	if (hasRequiredGetMirrorValuesToKeepTextRightSideUp) return getMirrorValuesToKeepTextRightSideUp;
	hasRequiredGetMirrorValuesToKeepTextRightSideUp = 1;
	Object.defineProperty(getMirrorValuesToKeepTextRightSideUp, "__esModule", { value: true });
	getMirrorValuesToKeepTextRightSideUp.getMirrorValuesToKeepTextRightSideUp = getMirrorValuesToKeepTextRightSideUp$1;
	function getMirrorValuesToKeepTextRightSideUp$1(angleDegrees) {
	    if (angleDegrees - Number.EPSILON >= 180 || (angleDegrees < 0 && angleDegrees !== -90)) {
	        return [true, true];
	    }
	    else {
	        return [false, false];
	    }
	}
	
	return getMirrorValuesToKeepTextRightSideUp;
}

var parseTextTransforms = {};

var hasRequiredParseTextTransforms;

function requireParseTextTransforms () {
	if (hasRequiredParseTextTransforms) return parseTextTransforms;
	hasRequiredParseTextTransforms = 1;
	Object.defineProperty(parseTextTransforms, "__esModule", { value: true });
	parseTextTransforms.parseTextTransforms = parseTextTransforms$1;
	const sigma_engine_shared_1 = requireDist$1();
	const ModelText_1 = requireModelText();
	function parseTextTransforms$1(text) {
	    const rawValue = text.value;
	    const changed = String(rawValue).replace(/\\n/g, '\n').trimEnd();
	    if ((0, sigma_engine_shared_1.isNetRef)(rawValue)) {
	        rawValue.setName(changed);
	    }
	    else {
	        text.value = changed;
	    }
	    const encoded = (0, ModelText_1.encodeRawText)({ raw: changed, dialects: [ModelText_1.TextDialect.Kicad] });
	    if (encoded.textTransforms.length > 0) {
	        text.textTransforms = encoded.textTransforms;
	    }
	    return text;
	}
	
	return parseTextTransforms;
}

var hasRequiredV6ParseLabel;

function requireV6ParseLabel () {
	if (hasRequiredV6ParseLabel) return v6ParseLabel;
	hasRequiredV6ParseLabel = 1;
	Object.defineProperty(v6ParseLabel, "__esModule", { value: true });
	v6ParseLabel.parseTextElement = parseTextElement;
	v6ParseLabel.parseLabel = parseLabel;
	v6ParseLabel.parseTextBox = parseTextBox;
	v6ParseLabel.extractLabelInfo = extractLabelInfo;
	const Util_1 = requireUtil();
	const getMirrorValuesToKeepTextRightSideUp_1 = requireGetMirrorValuesToKeepTextRightSideUp();
	const parseTextTransforms_1 = requireParseTextTransforms();
	const v6Common_1 = requireV6Common();
	const sigma_engine_shared_1 = requireDist$1();
	const Utils_1 = requireUtils$4();
	const Transform_1 = requireTransform();
	const Constants_1 = requireConstants$5();
	const sigma_engine_shared_2 = requireDist$1();
	function parseTextElement(node, isModule, warnings, defaultLayerName, layerSelector, keepRightSideUp) {
	    let [value] = node.values;
	    value = (0, Util_1.removeQuotes)(value);
	    const { pos, direction, justify, fontSize, thickness, attr, mirror, layerName, textAlign } = extractLabelInfo(node, isModule, warnings);
	    let layer;
	    if (layerName) {
	        const layers = layerSelector.filterLayers(layerName);
	        if (layers[0]) {
	            layer = layers[0];
	        }
	    }
	    if (!layer) {
	        if (defaultLayerName) {
	            const layers = layerSelector.filterLayers(defaultLayerName);
	            if (layers[0]) {
	                layer = layers[0];
	            }
	        }
	        else {
	            layer = layerSelector.getLayerByType(sigma_engine_shared_2.DesignModels.UnknownLayerType.Other);
	        }
	    }
	    return {
	        drawing: (0, parseTextTransforms_1.parseTextTransforms)({
	            type: 'text',
	            value: value,
	            pos: pos,
	            angle: direction,
	            justify,
	            textAlign,
	            size: fontSize,
	            thickness: typeof thickness === 'number' ? thickness : 0,
	            attr,
	            mirror: keepRightSideUp ? (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction) : mirror,
	        }),
	        layers: [layer.id],
	    };
	}
	function parseLabel(model, node, isModule, warnings, netRefCtx, defaultLayerName, layerSelector, resolver, keepRightSideUp) {
	    let [value] = node.values;
	    value = (0, Util_1.removeQuotes)(value);
	    const { pos, direction, justify, fontSize, thickness, attr, mirror, layerName, textAlign } = extractLabelInfo(node, isModule, warnings);
	    let layer;
	    if (layerName) {
	        const layers = layerSelector.filterLayers(layerName);
	        if (layers[0]) {
	            layer = layers[0];
	        }
	    }
	    if (!layer) {
	        if (defaultLayerName) {
	            const layers = layerSelector.filterLayers(defaultLayerName);
	            if (layers[0]) {
	                layer = layers[0];
	            }
	        }
	        else {
	            layer = layerSelector.getLayerByType(sigma_engine_shared_2.DesignModels.UnknownLayerType.Other);
	        }
	    }
	    return {
	        drawing: (0, parseTextTransforms_1.parseTextTransforms)({
	            type: 'text',
	            value: sigma_engine_shared_1.NetRef.fromString(netRefCtx, resolver ? resolver.validateSourceNetName(model.designId, value, 'label') : value).strCast(),
	            pos: pos,
	            angle: direction,
	            justify,
	            textAlign,
	            size: fontSize,
	            thickness: typeof thickness === 'number' ? thickness : 0,
	            attr,
	            mirror: keepRightSideUp ? (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction) : mirror,
	        }),
	        layers: [layer.id],
	    };
	}
	function parseTextBox(node, isModule, warnings, defaultLayerName, layerSelector, keepRightSideUp) {
	    let [value] = node.values;
	    value = (0, Util_1.removeQuotes)(value);
	    let { pos, direction, justify, fontSize, thickness, attr, mirror, layerName, textAlign, size, stroke, fill } = extractLabelInfo(node, isModule, warnings, layerSelector);
	    if (!size) {
	        warnings.push(`${node.name} missing expected size attribute`);
	        size = [0, 0];
	    }
	    let layer;
	    if (layerName) {
	        const layers = layerSelector.filterLayers(layerName);
	        if (layers[0]) {
	            layer = layers[0];
	        }
	    }
	    if (!layer) {
	        if (defaultLayerName) {
	            const layers = layerSelector.filterLayers(defaultLayerName);
	            if (layers[0]) {
	                layer = layers[0];
	            }
	        }
	        else {
	            layer = layerSelector.getLayerByType(sigma_engine_shared_2.DesignModels.UnknownLayerType.Other);
	        }
	    }
	    const textDrawing = (0, parseTextTransforms_1.parseTextTransforms)({
	        type: 'text',
	        value,
	        pos: [0, 0],
	        angle: direction,
	        justify,
	        textAlign,
	        size: fontSize,
	        padding: [fontSize, fontSize],
	        bounds: size.map((v) => Math.abs(v)),
	        thickness: typeof thickness === 'number' ? thickness : 0,
	        attr,
	        mirror: keepRightSideUp ? (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction) : mirror,
	    });
	    const rectDrawing = {
	        type: 'rect',
	        start: [0, 0],
	        end: size.map((v) => Math.abs(v)),
	        filled: fill?.filled === 1,
	        strokeWidth: (0, Utils_1.max)(stroke?.width || 0, Constants_1.SEGMENT_MIN_WIDTH),
	        colorOverride: fill?.colorOverride,
	    };
	    const groupDrawing = {
	        type: 'group',
	        pos: (0, Transform_1.translatePoint)(pos, size.map((v) => Math.min(0, v))),
	        angle: direction,
	        drawings: [rectDrawing, textDrawing],
	    };
	    return {
	        drawing: groupDrawing,
	        layers: [layer.id],
	    };
	}
	function extractLabelInfo(node, isModule, warnings, layerSelector, defaultFillLayer = sigma_engine_shared_2.DesignModels.UnknownLayerType.Other) {
	    let pos = [0, 0];
	    let size;
	    let fontSize = 0;
	    let direction = 0;
	    const justify = [0, 0];
	    let justifyNodeValues;
	    let shape;
	    let thickness;
	    let attr;
	    const mirror = [false, false];
	    let layerName;
	    let stroke;
	    let fill;
	    const flipY = isModule ? -1 : 1;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'at': {
	                pos = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
	                if (n.values.length > 2) {
	                    direction = parseFloat(n.values[2]);
	                }
	                break;
	            }
	            case 'size': {
	                size = [n.values[0], n.values[1]].map((v) => parseFloat(v));
	                break;
	            }
	            case 'effects': {
	                const fontNode = n.nodes.find((n) => n.name === 'font');
	                if (fontNode) {
	                    const sizeNode = fontNode.nodes.find((n) => n.name === 'size');
	                    if (sizeNode) {
	                        fontSize = parseFloat(sizeNode.values[0]);
	                    }
	                    const thicknessNode = fontNode.nodes.find((n) => n.name === 'thickness');
	                    if (thicknessNode) {
	                        thickness = parseFloat(thicknessNode.values[0]);
	                    }
	                    if (fontNode.values.indexOf('bold') !== -1) {
	                        if (!attr) {
	                            attr = [];
	                        }
	                        attr.push('bold');
	                    }
	                    if (fontNode.values.indexOf('italic') !== -1) {
	                        if (!attr) {
	                            attr = [];
	                        }
	                        attr.push('italic');
	                    }
	                }
	                justifyNodeValues = justifyNodeValues || n.nodes.find((n) => n.name === 'justify')?.values;
	                break;
	            }
	            case 'shape': {
	                switch (n.values[0]) {
	                    case 'input':
	                    case 'tri_state':
	                    case 'output':
	                    case 'bidirectional':
	                    case 'passive':
	                        shape = n.values[0];
	                        break;
	                    default:
	                        warnings.push(`Unknown ${node.name} shape: ${n.values[0]}`);
	                }
	                break;
	            }
	            case 'layer':
	                layerName = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            case 'stroke':
	                stroke = (0, v6Common_1.parseStrokeDirective)(n);
	                break;
	            case 'fill':
	                if (layerSelector) {
	                    fill = (0, v6Common_1.parseFillDirective)(n, layerSelector, defaultFillLayer);
	                }
	                break;
	        }
	    });
	    if (justifyNodeValues) {
	        for (const val of justifyNodeValues) {
	            switch (val) {
	                case 'right':
	                    justify[0] = 1;
	                    break;
	                case 'left':
	                    justify[0] = -1;
	                    break;
	                case 'bottom':
	                    justify[1] = 1;
	                    break;
	                case 'top':
	                    justify[1] = -1;
	                    break;
	                case 'mirror':
	                    mirror[1] = true;
	                    break;
	            }
	        }
	    }
	    let textAlign = 'left';
	    switch (justify[0]) {
	        case 0:
	            textAlign = 'center';
	            break;
	        case 1:
	            textAlign = 'right';
	            break;
	    }
	    return {
	        pos,
	        direction,
	        justify,
	        fontSize,
	        shape,
	        thickness,
	        attr,
	        mirror,
	        layerName,
	        textAlign,
	        size,
	        stroke,
	        fill,
	    };
	}
	
	return v6ParseLabel;
}

var v6ExtractPolygon = {};

var hasRequiredV6ExtractPolygon;

function requireV6ExtractPolygon () {
	if (hasRequiredV6ExtractPolygon) return v6ExtractPolygon;
	hasRequiredV6ExtractPolygon = 1;
	Object.defineProperty(v6ExtractPolygon, "__esModule", { value: true });
	v6ExtractPolygon.extractPolygon = extractPolygon;
	const Util_1 = requireUtil();
	const v6Common_1 = requireV6Common();
	function extractPolygon(isModule, node, ensureCompleted = false) {
	    const points = [];
	    let strokeWidth = 1;
	    let fillType = isModule ? 'solid' : 'none';
	    let style;
	    let layerName;
	    const flipY = isModule ? -1 : 1;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'pts': {
	                n.nodes.forEach((n2) => {
	                    switch (n2.name) {
	                        case 'xy': {
	                            points.push([parseFloat(n2.values[0]), flipY * parseFloat(n2.values[1])]);
	                            break;
	                        }
	                    }
	                });
	                break;
	            }
	            case 'stroke': {
	                n.nodes.forEach((n2) => {
	                    switch (n2.name) {
	                        case 'width': {
	                            strokeWidth = parseFloat(n2.values[0]);
	                            break;
	                        }
	                        case 'type': {
	                            style = (0, v6Common_1.parseKicadLineType)(n2.values[0]) || (isModule ? undefined : 'dash');
	                            break;
	                        }
	                    }
	                });
	                break;
	            }
	            case 'fill': {
	                n.nodes.forEach((n2) => {
	                    switch (n2.name) {
	                        case 'type': {
	                            fillType = n2.values[0];
	                            break;
	                        }
	                    }
	                });
	                if (n.values.length > 0) {
	                    fillType = n.values[0];
	                }
	                break;
	            }
	            case 'layer': {
	                layerName = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	            case 'width': {
	                strokeWidth = parseFloat(n.values[0]);
	                break;
	            }
	            case 'type': {
	                style = (0, v6Common_1.parseKicadLineType)(n.values[0]) || (isModule ? undefined : 'dash');
	                break;
	            }
	        }
	    });
	    if (ensureCompleted && points.length >= 2) {
	        const lastPoint = points[points.length - 1];
	        if (points[0][0] !== lastPoint[0] || points[0][1] !== lastPoint[1]) {
	            points.push([points[0][0], points[0][1]]);
	        }
	    }
	    return { points, strokeWidth, style, fillType, layerName };
	}
	
	return v6ExtractPolygon;
}

var v6ParseTitleBlock = {};

var hasRequiredV6ParseTitleBlock;

function requireV6ParseTitleBlock () {
	if (hasRequiredV6ParseTitleBlock) return v6ParseTitleBlock;
	hasRequiredV6ParseTitleBlock = 1;
	Object.defineProperty(v6ParseTitleBlock, "__esModule", { value: true });
	v6ParseTitleBlock.parseTitleBlock = parseTitleBlock;
	v6ParseTitleBlock.extractTitleBlock = extractTitleBlock;
	const Util_1 = requireUtil();
	function parseTitleBlock(model, n) {
	    const blockInfo = extractTitleBlock(n);
	    if (blockInfo.title) {
	        model.metadata.title = blockInfo.title;
	    }
	    if (blockInfo.date) {
	        model.metadata.date = blockInfo.date;
	    }
	    if (blockInfo.revision) {
	        model.metadata.revision = blockInfo.revision;
	    }
	    if (blockInfo.company) {
	        model.metadata.company = blockInfo.company;
	    }
	    for (let i = 0; i < blockInfo.comments.length; ++i) {
	        model.metadata[`comment ${i + 1}`] = blockInfo.comments[i];
	    }
	}
	function extractTitleBlock(n) {
	    const blockInfo = {
	        comments: [],
	    };
	    for (const node of n.nodes) {
	        switch (node.name) {
	            case 'title':
	                blockInfo.title = (0, Util_1.removeQuotes)(node.values[0]);
	                break;
	            case 'date':
	                blockInfo.date = (0, Util_1.removeQuotes)(node.values[0]);
	                break;
	            case 'rev':
	                blockInfo.revision = (0, Util_1.removeQuotes)(node.values[0]);
	                break;
	            case 'company':
	                blockInfo.company = (0, Util_1.removeQuotes)(node.values[0]);
	                break;
	            case 'comment':
	                blockInfo.comments.push((0, Util_1.removeQuotes)(node.values[1]));
	                break;
	        }
	    }
	    return blockInfo;
	}
	
	return v6ParseTitleBlock;
}

var v6ParseCircle = {};

var hasRequiredV6ParseCircle;

function requireV6ParseCircle () {
	if (hasRequiredV6ParseCircle) return v6ParseCircle;
	hasRequiredV6ParseCircle = 1;
	Object.defineProperty(v6ParseCircle, "__esModule", { value: true });
	v6ParseCircle.readCircle = readCircle;
	const Util_1 = requireUtil();
	const v6Common_1 = requireV6Common();
	function readCircle(node, defaultLayerName = '') {
	    let x = 0, y = 0;
	    let endX = 0, endY = 0;
	    let layer = defaultLayerName;
	    let width = 0;
	    let filled = false;
	    let radius;
	    let lineStyle;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'center': {
	                x = parseFloat(n.values[0]);
	                y = parseFloat(n.values[1]);
	                break;
	            }
	            case 'end': {
	                endX = parseFloat(n.values[0]);
	                endY = parseFloat(n.values[1]);
	                break;
	            }
	            case 'layer': {
	                layer = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	            case 'width': {
	                width = parseFloat(n.values[0]);
	                break;
	            }
	            case 'radius': {
	                radius = parseFloat(n.values[0]);
	                break;
	            }
	            case 'stroke': {
	                const stroke = (0, v6Common_1.parseStrokeDirective)(n);
	                width = stroke.width;
	                lineStyle = stroke.type;
	                break;
	            }
	            case 'fill': {
	                if (n.nodes.length > 0) {
	                    const typeNode = n.nodes.find((v) => v.name === 'type');
	                    if (typeNode) {
	                        filled = n.values[0] === 'solid';
	                    }
	                }
	                else {
	                    filled = n.values[0] === 'solid';
	                }
	                break;
	            }
	        }
	    });
	    const circle = {
	        type: 'circle',
	        center: [x, y],
	        radius: typeof radius === 'number' ? radius : Math.hypot(endX - x, endY - y),
	        filled: filled ? 1 : undefined,
	        width,
	        metadata: {
	            lineStyle,
	        },
	    };
	    return { layer, circle };
	}
	
	return v6ParseCircle;
}

var v6schema = {};

var constants = {};

var hasRequiredConstants;

function requireConstants () {
	if (hasRequiredConstants) return constants;
	hasRequiredConstants = 1;
	Object.defineProperty(constants, "__esModule", { value: true });
	constants.KicadNetlistSchematicModel = constants.META_KICAD_UNIT = constants.MetadataKicadIsRoot = constants.KicadDocLibPreamble = constants.KicadSymbolLibTablePreamble = constants.KicadFootprintLibTablePreamble = constants.EESchemaLibPreamble = constants.EESchemaSchPreamble = constants.KicadSchPreamble = constants.KicadPcbPreamble = void 0;
	constants.KicadPcbPreamble = '(kicad_pcb';
	constants.KicadSchPreamble = '(kicad_sch';
	constants.EESchemaSchPreamble = 'EESchema Schematic';
	constants.EESchemaLibPreamble = 'EESchema-LIBRARY';
	constants.KicadFootprintLibTablePreamble = '(fp_lib_table';
	constants.KicadSymbolLibTablePreamble = '(sym_lib_table';
	constants.KicadDocLibPreamble = 'EESchema-DOCLIB';
	constants.MetadataKicadIsRoot = 'kicadisroot';
	constants.META_KICAD_UNIT = 'kicad.unit';
	constants.KicadNetlistSchematicModel = 'netlist-schematic-model';
	
	return constants;
}

var project$2 = {};

var ComponentResolver = {};

var hasRequiredComponentResolver;

function requireComponentResolver () {
	if (hasRequiredComponentResolver) return ComponentResolver;
	hasRequiredComponentResolver = 1;
	Object.defineProperty(ComponentResolver, "__esModule", { value: true });
	ComponentResolver.KicadComponentResolver = void 0;
	const Util_1 = requireUtil();
	class KicadComponentResolver {
	    constructor(project, resolutionOpts = {}) {
	        this.project = project;
	        this.resolutionOpts = resolutionOpts;
	        this.componentMap = new Map();
	        this.nextSequentialNameMap = new Map();
	    }
	    addComponentPart(module) {
	        this.componentMap.set(module.ref, module.componentRef);
	    }
	    getComponentForFootprint(module, footprint, model) {
	        if (module) {
	            const path = this.formatIdentifier(module);
	            if (this.componentMap.has(path)) {
	                const componentRef = this.componentMap.get(path);
	                if (this.project.components[componentRef]) {
	                    return this.project.components[componentRef];
	                }
	                else {
	                    model.warnings.push(`Did not resolve component for component ref ${componentRef} in fp ${path}`);
	                }
	            }
	            else {
	                model.warnings.push(`Did not resolve component for footprint path: ${path}`);
	            }
	        }
	        return (0, Util_1.findComponentByFootprint)(this.project, footprint, model);
	    }
	    formatIdentifier(module) {
	        return module.ref;
	    }
	    getNextSequentialName(designId, pattern, leftPad = 3) {
	        if (!this.nextSequentialNameMap.has(pattern)) {
	            this.nextSequentialNameMap.set(pattern, 1);
	        }
	        const nextName = this.nextSequentialNameMap.get(pattern);
	        this.nextSequentialNameMap.set(pattern, nextName + 1);
	        return this.validateAnnotatedName(designId, `${pattern}${nextName.toString().padStart(leftPad, '0')}`);
	    }
	    validateAnnotatedName(_designId, moduleName) {
	        return moduleName;
	    }
	    validateSourceModuleName(_designId, moduleName, _moduleRef) {
	        return moduleName;
	    }
	    validateSourceNetName(_designId, netName, _type) {
	        return netName;
	    }
	    validateSourceModuleRef(_designId, ref) {
	        return ref;
	    }
	    updateAltiumSupplySymbol(component) {
	        if (this.isPowerFlag(component.metadata)) {
	            return { ...component, isVirtual: true };
	        }
	        return component;
	    }
	    isPowerFlag(metadata) {
	        return metadata?.['ki_keywords'] === 'power-flag';
	    }
	}
	ComponentResolver.KicadComponentResolver = KicadComponentResolver;
	
	return ComponentResolver;
}

var schematic$1 = {};

var hasRequiredSchematic$1;

function requireSchematic$1 () {
	if (hasRequiredSchematic$1) return schematic$1;
	hasRequiredSchematic$1 = 1;
	Object.defineProperty(schematic$1, "__esModule", { value: true });
	schematic$1.parseSchematic = parseSchematic;
	schematic$1.preParseSchematicSheets = preParseSchematicSheets;
	const project_1 = requireProject$2();
	const v6schema_1 = requireV6schema();
	function parseSchematic(def, parser, project, sch, resolver, netRefCtx, netCombobulationOpts, extractedImages) {
	    if (sch.contents.startsWith('EESchema')) {
	        throw new Error('EESchema schematics are not. Update your KiCad version.');
	    }
	    else {
	        return (0, v6schema_1.parseSchematic)(def, parser, project, sch, resolver, netRefCtx, netCombobulationOpts, extractedImages);
	    }
	}
	async function preParseSchematicSheets(def, parser, warnings) {
	    const project = Object.values(def.other ?? {}).find((f) => f.type === 'project');
	    const configuredProjectName = project ? schematicProjectName(project.originalFileName) : undefined;
	    const { modernFiles: parsedFiles, legacySheets } = await parseSchematicFiles(def, parser, warnings, configuredProjectName);
	    const rootFiles = getRootFiles(parsedFiles, project?.originalFileName);
	    const outSheets = [...legacySheets];
	    const pendingSheets = [];
	    const expandedPaths = new Set();
	    const reachedFiles = new Set();
	    const rootUuidCounts = new Map();
	    for (const rootFile of rootFiles) {
	        rootUuidCounts.set(rootFile.mainSheet.uuid, (rootUuidCounts.get(rootFile.mainSheet.uuid) ?? 0) + 1);
	        const rootPath = (0, project_1.joinKicadInstancePath)('', rootFile.mainSheet.uuid);
	        const root = {
	            ...rootFile.mainSheet,
	            instancePath: rootPath,
	            annotationPath: rootPath,
	            netPath: '/',
	            projectName: configuredProjectName ?? schematicProjectName(rootFile.mainSheet.filename),
	        };
	        pendingSheets.push({ sheet: root });
	    }
	    const cycleWarnings = new Set();
	    let pendingIndex = 0;
	    while (pendingIndex < pendingSheets.length || !project) {
	        if (pendingIndex >= pendingSheets.length) {
	            const unreachableEntry = [...parsedFiles.entries()]
	                .filter(([filename]) => !reachedFiles.has(filename))
	                .sort(([left], [right]) => left.localeCompare(right))[0];
	            if (!unreachableEntry) {
	                break;
	            }
	            const [unreachableFilename, unreachableFile] = unreachableEntry;
	            const rootPath = (0, project_1.joinKicadInstancePath)('', unreachableFile.mainSheet.uuid);
	            rootUuidCounts.set(unreachableFile.mainSheet.uuid, (rootUuidCounts.get(unreachableFile.mainSheet.uuid) ?? 0) + 1);
	            pendingSheets.push({
	                sheet: {
	                    ...unreachableFile.mainSheet,
	                    instancePath: rootPath,
	                    annotationPath: rootPath,
	                    netPath: '/',
	                    projectName: schematicProjectName(unreachableFilename),
	                },
	            });
	            warnings.push(`Sheet ${unreachableFilename} is unreachable from any root; adding it as a root`);
	            continue;
	        }
	        const pending = pendingSheets[pendingIndex];
	        pendingIndex += 1;
	        const { sheet } = pending;
	        const { instancePath } = sheet;
	        if (!instancePath) {
	            continue;
	        }
	        const expansionKey = `${sheet.filename}:${instancePath}`;
	        if (expandedPaths.has(expansionKey)) {
	            continue;
	        }
	        expandedPaths.add(expansionKey);
	        reachedFiles.add(sheet.filename);
	        outSheets.push(sheet);
	        const sourceFile = parsedFiles.get(sheet.filename);
	        if (!sourceFile) {
	            continue;
	        }
	        for (const templates of childTemplatesBySymbol(sourceFile.childSheets)) {
	            const activeTemplates = selectProjectTemplates(templates, sheet.projectName);
	            const parentAnnotationPath = sheet.annotationPath ?? instancePath;
	            const matchingTemplate = activeTemplates.find((childTemplate) => childTemplate.parentInstancePath === parentAnnotationPath);
	            const childTemplate = matchingTemplate ?? activeTemplates[0];
	            if (!childTemplate) {
	                continue;
	            }
	            if (hasAncestorFile(pending, childTemplate.filename)) {
	                const warning = `Sheet hierarchy contains a cycle at ${childTemplate.filename}`;
	                if (!cycleWarnings.has(warning)) {
	                    warnings.push(warning);
	                    cycleWarnings.add(warning);
	                }
	                continue;
	            }
	            const childAnnotationPath = matchingTemplate?.annotationPath ?? (0, project_1.joinKicadInstancePath)(parentAnnotationPath, childTemplate.uuid);
	            const legacySheetInstance = legacySheetInstanceForPath(sheet.legacySheetInstances, childAnnotationPath);
	            if (!matchingTemplate && !legacySheetInstance) {
	                warnings.push(`Sheet ${sheet.filename} has no child instance matching ${parentAnnotationPath} for ${childTemplate.filename}; expanding symbol`);
	            }
	            const occurrenceSegment = (childTemplate.sourceSymbolOccurrence ?? 1) > 1 ?
	                `${childTemplate.uuid}~${childTemplate.sourceSymbolOccurrence}`
	                : childTemplate.uuid;
	            const childInstancePath = (0, project_1.joinKicadInstancePath)(instancePath, occurrenceSegment);
	            const childFile = parsedFiles.get(childTemplate.filename);
	            pendingSheets.push({
	                sheet: {
	                    ...childTemplate,
	                    instancePath: childInstancePath,
	                    annotationPath: childAnnotationPath,
	                    parentInstancePath: instancePath,
	                    projectName: sheet.projectName,
	                    netPath: `${sheet.netPath}${childTemplate.name}/`,
	                    contents: childFile?.mainSheet.contents,
	                    libContents: childFile?.mainSheet.libContents,
	                    legacySymbolInstances: sheet.legacySymbolInstances,
	                    legacySheetInstances: sheet.legacySheetInstances,
	                    page: matchingTemplate?.page ?? legacySheetInstance?.page,
	                },
	                parent: pending,
	            });
	        }
	    }
	    for (const [filename] of parsedFiles) {
	        if (reachedFiles.has(filename)) {
	            continue;
	        }
	        if (project) {
	            const projectRoot = rootFiles[0]?.mainSheet.filename ?? '<unknown>';
	            warnings.push(`Sheet ${filename} is unreachable from project root ${projectRoot}, skipping`);
	        }
	    }
	    for (const [uuid, count] of rootUuidCounts) {
	        if (count > 1) {
	            warnings.push(`Sheet roots share UUID ${uuid}; retaining each root file as a separate design`);
	        }
	    }
	    const childUuidCounts = new Map();
	    const childInstanceIdentityCounts = new Map();
	    for (const sheet of outSheets) {
	        if (sheet.parentInstancePath) {
	            const sourceUuid = sheet.sourceSheetUuid ?? sheet.uuid;
	            childUuidCounts.set(sourceUuid, (childUuidCounts.get(sourceUuid) ?? 0) + 1);
	            if (sheet.instancePath) {
	                const identity = `${sourceUuid}:${sheet.instancePath}`;
	                childInstanceIdentityCounts.set(identity, (childInstanceIdentityCounts.get(identity) ?? 0) + 1);
	            }
	        }
	    }
	    const allocatedDesignIds = new Set();
	    for (const sheet of outSheets) {
	        const sheetUuid = sheet.sourceSheetUuid ?? sheet.uuid;
	        if (sheet.instancePath && sheet.parentInstancePath && (childUuidCounts.get(sheetUuid) ?? 0) > 1) {
	            const identity = `${sheetUuid}:${sheet.instancePath}`;
	            const idPath = (childInstanceIdentityCounts.get(identity) ?? 0) > 1 ?
	                `${sheet.filename}:${sheet.instancePath}`
	                : sheet.instancePath;
	            sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, idPath);
	        }
	        if (allocatedDesignIds.has(sheet.uuid)) {
	            const instanceIdentity = sheet.instancePath ?? sheet.filename;
	            sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, instanceIdentity);
	            if (allocatedDesignIds.has(sheet.uuid)) {
	                sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, `${sheet.filename}:${instanceIdentity}`);
	            }
	            let collisionIndex = 2;
	            while (allocatedDesignIds.has(sheet.uuid)) {
	                sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, `${sheet.filename}:${instanceIdentity}:${collisionIndex}`);
	                collisionIndex += 1;
	            }
	            warnings.push(`Sheet ${sheet.filename} design ID ${sheetUuid} collides with another design; assigned ${sheet.uuid}`);
	        }
	        allocatedDesignIds.add(sheet.uuid);
	    }
	    const sourceInstanceCounts = new Map();
	    for (const sheet of outSheets) {
	        sourceInstanceCounts.set(sheet.filename, (sourceInstanceCounts.get(sheet.filename) ?? 0) + 1);
	    }
	    for (const sheet of outSheets) {
	        sheet.sourceInstanceCount = sourceInstanceCounts.get(sheet.filename);
	    }
	    assignSheetPages(outSheets, warnings);
	    return outSheets;
	}
	function kicadSheetInstanceDesignId(sheetUuid, instancePath) {
	    return `kicad_${sheetUuid}_${instancePathHash(instancePath)}`;
	}
	function instancePathHash(instancePath) {
	    let first = 0x811c9dc5;
	    let second = 0x01000193;
	    for (let index = 0; index < instancePath.length; index += 1) {
	        const character = instancePath.charCodeAt(index);
	        first = Math.imul(first ^ character, 0x01000193);
	        second = Math.imul(second ^ character, 0x85ebca6b);
	    }
	    return `${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`.slice(0, 12);
	}
	async function parseSchematicFiles(def, parser, warnings, projectName) {
	    const parsedFiles = new Map();
	    const legacySheets = [];
	    for (const [schId, sch] of Object.entries(def.schematics)) {
	        const contentsText = await sch.contents.text();
	        if (contentsText.startsWith('EESchema')) {
	            legacySheets.push({
	                numberingOrder: 0,
	                uuid: schId,
	                filename: schId,
	                name: schId,
	                contents: contentsText,
	                libContents: sch.lib,
	                netPath: '',
	            });
	            continue;
	        }
	        const [mainSheet, ...childSheets] = (0, v6schema_1.preParseSchematicSheet)(schId, contentsText, parser, warnings, projectName);
	        if (!mainSheet) {
	            continue;
	        }
	        mainSheet.contents = contentsText;
	        mainSheet.libContents = sch.lib;
	        mainSheet.projectName = projectName;
	        const sourceSymbolsByUuid = new Map();
	        for (const childSheet of childSheets) {
	            const sourceSymbols = sourceSymbolsByUuid.get(childSheet.uuid) ?? new Set();
	            sourceSymbols.add(childSheet.sourceSymbolKey ?? childSheet.uuid);
	            sourceSymbolsByUuid.set(childSheet.uuid, sourceSymbols);
	        }
	        for (const [uuid, sourceSymbols] of sourceSymbolsByUuid) {
	            if (sourceSymbols.size > 1) {
	                warnings.push(`Sheet ${mainSheet.filename} contains multiple sheet symbols with UUID ${uuid}; expanding each symbol`);
	            }
	        }
	        parsedFiles.set(mainSheet.filename, { mainSheet, childSheets });
	    }
	    return { modernFiles: parsedFiles, legacySheets };
	}
	function getRootFiles(parsedFiles, projectFileName) {
	    const referencedFiles = getReferencedFiles(parsedFiles);
	    const roots = [...parsedFiles.entries()]
	        .filter(([filename]) => !referencedFiles.has(filename))
	        .map(([, file]) => file);
	    const projectRoot = projectFileName ? parsedFiles.get(projectFileName.replace(/\.[^./]*$/, '.kicad_sch')) : undefined;
	    if (projectRoot) {
	        return [projectRoot];
	    }
	    if (roots.length > 0) {
	        return roots;
	    }
	    return [...parsedFiles.entries()]
	        .sort(([left], [right]) => left.localeCompare(right))
	        .slice(0, 1)
	        .map(([, file]) => file);
	}
	function getReferencedFiles(parsedFiles) {
	    return new Set([...parsedFiles.values()].flatMap((file) => file.childSheets.map((sheet) => sheet.filename)));
	}
	function childTemplatesBySymbol(childSheets) {
	    const templates = new Map();
	    for (const sheet of childSheets) {
	        const sourceSymbolKey = sheet.sourceSymbolKey ?? sheet.uuid;
	        const entries = templates.get(sourceSymbolKey) ?? [];
	        entries.push(sheet);
	        templates.set(sourceSymbolKey, entries);
	    }
	    return [...templates.values()];
	}
	function selectProjectTemplates(templates, projectName) {
	    const matchingTemplates = projectName ? templates.filter((template) => template.instanceProjectName === projectName) : [];
	    return matchingTemplates.length > 0 ? matchingTemplates : templates;
	}
	function hasAncestorFile(pending, filename) {
	    let current = pending;
	    while (current) {
	        if (current.sheet.filename === filename) {
	            return true;
	        }
	        current = current.parent;
	    }
	    return false;
	}
	function legacySheetInstanceForPath(instances, annotationPath) {
	    const legacyPath = (0, project_1.kicadInstancePathWithoutRoot)(annotationPath);
	    return instances?.find((instance) => (0, project_1.joinKicadInstancePath)(instance.path, '') === legacyPath);
	}
	function assignSheetPages(sheets, warnings) {
	    const rootPages = new Set();
	    for (const sheet of sheets) {
	        if (sheet.parentInstancePath) {
	            continue;
	        }
	        const page = positivePageNumber(sheet.page) ?? positivePageNumber(sheet.numberingOrder);
	        if (page) {
	            rootPages.add(page);
	        }
	    }
	    const takenPages = new Set(rootPages);
	    const childSheets = sheets
	        .filter((sheet) => Boolean(sheet.parentInstancePath))
	        .sort((left, right) => {
	        const pathOrder = (left.instancePath ?? '').localeCompare(right.instancePath ?? '');
	        return pathOrder !== 0 ? pathOrder : left.filename.localeCompare(right.filename);
	    });
	    const validPageOwners = new Map();
	    let highestPage = Math.max(0, ...rootPages);
	    for (const sheet of childSheets) {
	        const page = positivePageNumber(sheet.page);
	        if (page) {
	            highestPage = Math.max(highestPage, page);
	            if (!rootPages.has(page) && !validPageOwners.has(page)) {
	                validPageOwners.set(page, sheet);
	            }
	        }
	    }
	    for (const [page, sheet] of validPageOwners) {
	        sheet.page = page;
	        takenPages.add(page);
	    }
	    for (const sheet of childSheets) {
	        const page = positivePageNumber(sheet.page);
	        if (page && validPageOwners.get(page) === sheet) {
	            continue;
	        }
	        const reportedPage = sheet.page ?? '<missing>';
	        highestPage += 1;
	        while (takenPages.has(highestPage)) {
	            highestPage += 1;
	        }
	        sheet.page = highestPage;
	        takenPages.add(highestPage);
	        warnings.push(`Sheet ${sheet.filename} has duplicate or invalid page ${reportedPage}; assigned page ${highestPage}`);
	    }
	}
	function positivePageNumber(value) {
	    if (typeof value === 'number') {
	        return Number.isInteger(value) && value > 0 ? value : undefined;
	    }
	    if (typeof value === 'string' && /^[1-9]\d*$/.test(value)) {
	        return Number.parseInt(value, 10);
	    }
	    return undefined;
	}
	function schematicProjectName(filename) {
	    if (!filename) {
	        return undefined;
	    }
	    const baseName = filename.slice(filename.lastIndexOf('/') + 1);
	    const extensionIndex = baseName.lastIndexOf('.');
	    return extensionIndex > 0 ? baseName.slice(0, extensionIndex) : baseName;
	}
	
	return schematic$1;
}

var Font = {};

var hasRequiredFont;

function requireFont () {
	if (hasRequiredFont) return Font;
	hasRequiredFont = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DefaultPackChars = void 0;
		exports.packModelFonts = packModelFonts;
		const fonts_1 = requireFonts();
		exports.DefaultPackChars = ' 01234567489';
		function packModelFonts(model) {
		    const fontChars = new Map();
		    function addFontChars(font, chars) {
		        if (!chars) {
		            return;
		        }
		        chars = chars.valueOf();
		        if (!fontChars.has(font)) {
		            fontChars.set(font, new Set(Array.from(exports.DefaultPackChars).map((c) => c.charCodeAt(0))));
		        }
		        const charSet = fontChars.get(font);
		        for (let i = 0; i < chars.length; ++i) {
		            if (chars[i] === '\n') {
		                continue;
		            }
		            const char = chars.charCodeAt(i);
		            if (!charSet.has(char)) {
		                charSet.add(char);
		            }
		        }
		    }
		    function parseDrawings(font, drawings) {
		        for (const drawing of drawings) {
		            switch (drawing.type) {
		                case 'text':
		                    addFontChars(drawing.font ?? font, drawing.value);
		                    break;
		                case 'label':
		                    addFontChars(font, drawing.value);
		                    break;
		                case 'pad':
		                    addFontChars(font, drawing.name);
		                    break;
		                case 'pin':
		                    addFontChars(font, drawing.name);
		                    if (drawing.drawings) {
		                        parseDrawings(font, drawing.drawings.map((d) => d.drawing));
		                    }
		                    break;
		                case 'schref':
		                    addFontChars(font, drawing.file);
		                    addFontChars(font, drawing.name);
		                    parseModule(font, 'schematic', drawing.drawModule);
		                    break;
		                case 'group':
		                    parseDrawings(font, drawing.drawings);
		                    break;
		            }
		        }
		    }
		    function parseModule(font, modelType, module) {
		        addFontChars(font, module.name);
		        for (const [, prop] of Object.entries(module.drawProperties)) {
		            addFontChars(font, prop.value);
		        }
		        const component = model.components[module.componentRef];
		        if (!component) {
		            return;
		        }
		        if (modelType === 'pcb') {
		            const footprint = component.footprints.find((f) => f.ref === module.footprintRef);
		            if (!footprint) {
		                return;
		            }
		            parseDrawings(font, footprint.drawings.flatMap((d) => d.drawing));
		            parseDrawings(font, footprint.pads);
		        }
		        else if (modelType === 'schematic') {
		            const symbol = component.symbols.find((s) => s.ref === module.footprintRef);
		            if (!symbol) {
		                return;
		            }
		            parseDrawings(font, symbol.drawings.flatMap((d) => d.drawing));
		            parseDrawings(font, symbol.pins);
		        }
		    }
		    for (const design of Object.values(model.designs)) {
		        if (!design.font) {
		            throw new Error(`Design ${design.designId} has no font`);
		        }
		        for (const [, drawings] of Object.entries(design.drawings)) {
		            parseDrawings(design.font.default, drawings);
		        }
		        for (const module of design.modules) {
		            parseModule(design.font.default, design.modelType, module);
		        }
		        if ('multiInstanceConfiguration' in design && design.multiInstanceConfiguration) {
		            for (const displayModule of design.multiInstanceConfiguration.multiInstanceModules) {
		                parseModule(design.font.default, design.modelType, displayModule);
		            }
		        }
		        if (Array.isArray(design.nets)) {
		            for (const net of design.nets) {
		                addFontChars(design.font.default, net);
		            }
		        }
		        else {
		            for (const net of Object.values(design.nets)) {
		                addFontChars(design.font.default, net.name);
		            }
		        }
		    }
		    for (const [font, chars] of fontChars) {
		        const existingFont = model.fonts?.fonts?.[font];
		        if (existingFont?.type === 'browser') {
		            continue;
		        }
		        const baseFont = (0, fonts_1.getFont)(font);
		        const projectFont = {
		            type: 'project',
		            ...baseFont,
		            name: font,
		            glyphs: {},
		        };
		        const charArray = Array.from(chars).sort();
		        let lastChar = Number.NEGATIVE_INFINITY;
		        let lastCharGroup;
		        const missingChars = [];
		        for (let i = 0; i < charArray.length; ++i) {
		            const char = charArray[i];
		            const glyph = baseFont.glyphs[char - baseFont.startChar];
		            if (!glyph) {
		                missingChars.push(char);
		                continue;
		            }
		            if (glyph === baseFont.unknownChar) {
		                continue;
		            }
		            if (lastCharGroup && char === lastChar + lastCharGroup.length) {
		                lastCharGroup.push(glyph);
		            }
		            else {
		                lastChar = char;
		                lastCharGroup = [glyph];
		                projectFont.glyphs[lastChar] = lastCharGroup;
		            }
		        }
		        if (missingChars.length > 0) {
		            model.warnings.push(`Missing ${missingChars.length} glyphs for font ${font}: ${missingChars.map((c) => String.fromCharCode(c)).join(',')}`);
		        }
		        model.fonts.fonts[font] = projectFont;
		    }
		    return model;
		}
		
	} (Font));
	return Font;
}

var RefableNet = {};

var hasRequiredRefableNet;

function requireRefableNet () {
	if (hasRequiredRefableNet) return RefableNet;
	hasRequiredRefableNet = 1;
	Object.defineProperty(RefableNet, "__esModule", { value: true });
	RefableNet.RefableMap = void 0;
	RefableNet.refableObject = refableObject;
	RefableNet.refableObjectValues = refableObjectValues;
	const sigma_engine_shared_1 = requireDist$1();
	function refableObject(obj, refCtx, combine) {
	    const m = new Map();
	    const target = {};
	    const returnObj = new Proxy(target, {
	        get(_target, prop) {
	            if (typeof prop !== 'string') {
	                switch (prop) {
	                    case Symbol.iterator:
	                        return m.entries();
	                }
	                return m[prop];
	            }
	            if (prop === 'toJSON') {
	                return () => {
	                    const o = {};
	                    m.forEach((v, k) => {
	                        if (typeof o[k.valueOf()] !== 'undefined') {
	                            o[k.valueOf()] = combine(k, o[k.valueOf()], v);
	                        }
	                        else {
	                            o[k.valueOf()] = v;
	                        }
	                    });
	                    return o;
	                };
	            }
	            const propAsNetRef = refCtx.existingOrNewRef(prop);
	            if (m.has(propAsNetRef)) {
	                return m.get(propAsNetRef);
	            }
	            for (const [key, value] of m.entries()) {
	                if (key.valueOf() === propAsNetRef.valueOf()) {
	                    return value;
	                }
	            }
	            return target[prop];
	        },
	        set(_target, prop, value) {
	            if (typeof prop !== 'string' && !(prop instanceof sigma_engine_shared_1.NetRef)) {
	                return false;
	            }
	            const propAsNetRef = (prop instanceof sigma_engine_shared_1.NetRef ?
	                prop
	                : refCtx.existingOrNewRef(prop));
	            m.set(propAsNetRef, value);
	            return true;
	        },
	        ownKeys() {
	            return Array.from(new Set(Array.from(m.keys()).map((o) => o.valueOf())));
	        },
	        has(_target, prop) {
	            if (prop === 'toJSON') {
	                return true;
	            }
	            if (typeof prop === 'string' || prop instanceof sigma_engine_shared_1.NetRef) {
	                const propAsNetRef = refCtx.existingOrNewRef(prop);
	                if (m.has(propAsNetRef)) {
	                    return m.get(propAsNetRef);
	                }
	                for (const [key, value] of m.entries()) {
	                    if (key.valueOf() === propAsNetRef.valueOf()) {
	                        return value;
	                    }
	                }
	            }
	            return m[prop];
	        },
	        getOwnPropertyDescriptor(_target, prop) {
	            if (typeof prop !== 'string') {
	                return undefined;
	            }
	            const propRef = refCtx.existingOrNewRef(prop);
	            if (m.has(propRef)) {
	                return {
	                    value: m.get(propRef),
	                    enumerable: true,
	                    writable: true,
	                    configurable: true,
	                };
	            }
	            return Object.getOwnPropertyDescriptor(target, prop);
	        },
	        deleteProperty(_target, prop) {
	            if (typeof prop !== 'string' && !(prop instanceof sigma_engine_shared_1.NetRef)) {
	                return false;
	            }
	            const propRef = refCtx.existingOrNewRef(prop);
	            if (m.has(propRef)) {
	                m.delete(propRef);
	                return true;
	            }
	            return false;
	        },
	    });
	    for (const [key, value] of Object.entries(obj)) {
	        returnObj[key] = value;
	    }
	    return returnObj;
	}
	function refableObjectValues(obj) {
	    const entries = obj[Symbol.iterator];
	    if (entries) {
	        return Array.from(entries, ([, value]) => value);
	    }
	    return Object.values(obj);
	}
	class RefableMap extends Map {
	    toJSON() {
	        const obj = {};
	        this.forEach((v, k) => {
	            obj[k.valueOf()] = v;
	        });
	        return obj;
	    }
	}
	RefableNet.RefableMap = RefableMap;
	
	return RefableNet;
}

var KicadHierarchyResolver = {};

var KicadNetNamingStrategy = {};

var IndividualGroupingRule = {};

var HierarchicalNetGroup = {};

var hasRequiredHierarchicalNetGroup;

function requireHierarchicalNetGroup () {
	if (hasRequiredHierarchicalNetGroup) return HierarchicalNetGroup;
	hasRequiredHierarchicalNetGroup = 1;
	Object.defineProperty(HierarchicalNetGroup, "__esModule", { value: true });
	HierarchicalNetGroup.HierarchicalNetGroup = void 0;
	class HierarchicalNetGroup$1 {
	    constructor() {
	        this.netsByInstance = new Map();
	        this.dimensions = new Set();
	        this.mutationVersion = 0;
	        this.physicalName = '';
	    }
	    addNet(instance, net) {
	        const existingNets = this.netsByInstance.get(instance);
	        let added = false;
	        if (existingNets) {
	            if (!existingNets.includes(net)) {
	                existingNets.push(net);
	                added = true;
	            }
	        }
	        else {
	            this.netsByInstance.set(instance, [net]);
	            added = true;
	        }
	        if (!added) {
	            return;
	        }
	        this.mutationVersion++;
	        for (const obj of net.netObjects) {
	            for (const endpoint of obj.endpoints) {
	                this.dimensions.add(endpoint.dimension);
	            }
	        }
	    }
	    contains(net) {
	        for (const nets of this.netsByInstance.values()) {
	            if (nets.includes(net)) {
	                return true;
	            }
	        }
	        return false;
	    }
	    containsNetName(netName) {
	        for (const nets of this.netsByInstance.values()) {
	            for (const net of nets) {
	                if (net.name.valueOf() === netName) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    containsConnection(connection, inInstance) {
	        for (const [instance, nets] of this.netsByInstance) {
	            if (inInstance && instance !== inInstance) {
	                continue;
	            }
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    if (connection.moduleRef && conn.connection.moduleRef !== connection.moduleRef) {
	                        continue;
	                    }
	                    if (connection.pinRef && conn.connection.pinRef !== connection.pinRef) {
	                        continue;
	                    }
	                    if (connection.moduleName && conn.connection.moduleName !== connection.moduleName) {
	                        continue;
	                    }
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    merge(instance, net) {
	        if (!this.contains(net)) {
	            this.addNet(instance, net);
	        }
	    }
	    removeNet(instance, net) {
	        const nets = this.netsByInstance.get(instance);
	        if (nets === undefined) {
	            return false;
	        }
	        const index = nets.indexOf(net);
	        if (index === -1) {
	            return false;
	        }
	        nets.splice(index, 1);
	        if (nets.length === 0) {
	            this.netsByInstance.delete(instance);
	        }
	        this.rebuildDimensions();
	        this.mutationVersion++;
	        return true;
	    }
	    getMutationVersion() {
	        return this.mutationVersion;
	    }
	    isEmpty() {
	        return this.netsByInstance.size === 0;
	    }
	    rebuildDimensions() {
	        this.dimensions.clear();
	        for (const nets of this.netsByInstance.values()) {
	            for (const net of nets) {
	                for (const obj of net.netObjects) {
	                    for (const endpoint of obj.endpoints) {
	                        this.dimensions.add(endpoint.dimension);
	                    }
	                }
	            }
	        }
	    }
	    getLocalNames() {
	        const names = [];
	        for (const nets of this.netsByInstance.values()) {
	            for (const net of nets) {
	                names.push(net.name.valueOf());
	            }
	        }
	        return names;
	    }
	    getConnections(inInstance) {
	        const connections = [];
	        for (const [instance, nets] of this.netsByInstance) {
	            if (inInstance && instance !== inInstance) {
	                continue;
	            }
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    connections.push(conn.connection);
	                }
	            }
	        }
	        return connections;
	    }
	    getNetLabels(searchInInstance) {
	        const labels = [];
	        for (const [instance, nets] of this.netsByInstance) {
	            if (searchInInstance && instance !== searchInInstance) {
	                continue;
	            }
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    labels.push(label);
	                }
	            }
	        }
	        return labels;
	    }
	    findNetForLabel(label) {
	        for (const nets of this.netsByInstance.values()) {
	            for (const net of nets) {
	                if (net.labels.includes(label)) {
	                    return net;
	                }
	            }
	        }
	        throw new Error('Label not found in group');
	    }
	    getNets() {
	        return this.netsByInstance;
	    }
	    hasLabelType(labelType, searchInInstance) {
	        for (const [instance, nets] of this.netsByInstance) {
	            if (searchInInstance && instance !== searchInInstance) {
	                continue;
	            }
	            for (const net of nets) {
	                if (net.labels.some((label) => label.labelType === labelType)) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    hasObjectsOnDimension(dimension) {
	        return this.dimensions.has(dimension);
	    }
	    hasObjectsOnDimensionMatching(predicate) {
	        for (const dimension of this.dimensions) {
	            if (predicate(dimension)) {
	                return true;
	            }
	        }
	        return false;
	    }
	}
	HierarchicalNetGroup.HierarchicalNetGroup = HierarchicalNetGroup$1;
	
	return HierarchicalNetGroup;
}

var NetNamingRules = {};

var hasRequiredNetNamingRules;

function requireNetNamingRules () {
	if (hasRequiredNetNamingRules) return NetNamingRules;
	hasRequiredNetNamingRules = 1;
	Object.defineProperty(NetNamingRules, "__esModule", { value: true });
	NetNamingRules.NetNamingRuleType = void 0;
	var NetNamingRuleType;
	(function (NetNamingRuleType) {
	    NetNamingRuleType[NetNamingRuleType["LocalNaming"] = 0] = "LocalNaming";
	    NetNamingRuleType[NetNamingRuleType["Group"] = 1] = "Group";
	    NetNamingRuleType[NetNamingRuleType["Naming"] = 2] = "Naming";
	    NetNamingRuleType[NetNamingRuleType["Finalize"] = 3] = "Finalize";
	})(NetNamingRuleType || (NetNamingRules.NetNamingRuleType = NetNamingRuleType = {}));
	
	return NetNamingRules;
}

var hasRequiredIndividualGroupingRule;

function requireIndividualGroupingRule () {
	if (hasRequiredIndividualGroupingRule) return IndividualGroupingRule;
	hasRequiredIndividualGroupingRule = 1;
	Object.defineProperty(IndividualGroupingRule, "__esModule", { value: true });
	IndividualGroupingRule.IndividualGroupingRule = void 0;
	const HierarchicalNetGroup_1 = requireHierarchicalNetGroup();
	const NetNamingRules_1 = requireNetNamingRules();
	class IndividualGroupingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, hierarchy) {
	        this.addGroupPerNet(manager, hierarchy);
	        return manager;
	    }
	    addGroupPerNet(manager, hierarchy) {
	        try {
	            hierarchy.combobulator.getFinalizedNets();
	        }
	        catch (error) {
	            hierarchy.combobulator.computeNets();
	        }
	        for (const net of hierarchy.combobulator.getFinalizedNets().values()) {
	            const group = new HierarchicalNetGroup_1.HierarchicalNetGroup();
	            group.addNet(hierarchy.instance, net);
	            manager.addGroup(group);
	        }
	        for (const child of hierarchy.children) {
	            this.addGroupPerNet(manager, child);
	        }
	    }
	}
	IndividualGroupingRule.IndividualGroupingRule = IndividualGroupingRule$1;
	
	return IndividualGroupingRule;
}

var NetCombobulator = {};

var CombobulationDimension = {};

var hasRequiredCombobulationDimension;

function requireCombobulationDimension () {
	if (hasRequiredCombobulationDimension) return CombobulationDimension;
	hasRequiredCombobulationDimension = 1;
	Object.defineProperty(CombobulationDimension, "__esModule", { value: true });
	CombobulationDimension.CombobulationDimension = void 0;
	const Transform_1 = requireTransform();
	class CombobulationDimension$1 {
	    constructor(name, opts = {}) {
	        this.name = name;
	        this.points = new Map();
	        this.wireObjects = new Map();
	        this.wireGrid = new Map();
	        this.pointRoundingPrecision = opts.pointRoundingPrecision ?? 0.1;
	        this.connectionTolerance = opts.connectionTolerance ?? this.pointRoundingPrecision;
	        this.boundingMargin = Math.max(this.pointRoundingPrecision, this.connectionTolerance);
	        this.explicitWireIntersections = opts.explicitWireIntersections ?? false;
	        this.wireCellSize = this.boundingMargin * 40;
	    }
	    addPoint(position, obj) {
	        position = [this.roundPoint(position[0]), this.roundPoint(position[1])];
	        if (!this.points.has(position[0])) {
	            this.points.set(position[0], new Map());
	        }
	        const xMap = this.points.get(position[0]);
	        if (!xMap.has(position[1])) {
	            xMap.set(position[1], [obj]);
	        }
	        else {
	            xMap.get(position[1])?.push(obj);
	        }
	    }
	    addWireObject(obj) {
	        if (!obj.wire) {
	            throw new Error('addWireObject called on a non-wire object');
	        }
	        const localEndpoints = obj.endpoints.filter((ep) => ep.dimension === this.name);
	        const geometry = localEndpoints.length >= 2 ? this.getWirePositions(obj.wire) : null;
	        this.wireObjects.set(obj, { geometry, intersecting: [] });
	        if (geometry) {
	            this.addWireToGrid(obj, geometry);
	        }
	        const intersecting = Array.from(this.computeWiresNetObjects([obj]));
	        this.wireObjects.get(obj).intersecting = intersecting;
	    }
	    getObjectsAtPosition(pos) {
	        const x = [];
	        x.push(this.points.get(pos[0]));
	        x.push(this.points.get(this.roundPoint(pos[0])));
	        return Array.from(new Set([
	            ...this.computeWiresNetObjects(this.getWiresNearPosition(pos), pos),
	            ...x
	                .flatMap((x) => [x?.get(pos[1]), x?.get(this.roundPoint(pos[1]))])
	                .flatMap((nos) => nos?.flatMap((no) => [no, no.wire ? this.wireObjects.get(no)?.intersecting : undefined]) ?? [])
	                .filter((no) => !!no)
	                .flat(),
	        ]));
	    }
	    *iteratePointGroups() {
	        for (const [x, yMap] of this.points.entries()) {
	            for (const [y, objects] of yMap.entries()) {
	                yield { x, y, objects };
	            }
	        }
	    }
	    getObjectsConnectedToPosition(startPos) {
	        const visited = new Set();
	        const positionQueue = [startPos];
	        const visitedPositions = new Set();
	        while (positionQueue.length > 0) {
	            const pos = positionQueue.shift();
	            const rx = this.roundPoint(pos[0]);
	            const ry = this.roundPoint(pos[1]);
	            const posKey = `${rx},${ry}`;
	            if (visitedPositions.has(posKey)) {
	                continue;
	            }
	            visitedPositions.add(posKey);
	            for (const obj of this.getObjectsAtPosition(pos)) {
	                if (visited.has(obj)) {
	                    continue;
	                }
	                visited.add(obj);
	                if (obj.wire) {
	                    const geometry = this.getEffectiveWireGeometry(obj);
	                    if (geometry) {
	                        positionQueue.push(geometry.from);
	                        positionQueue.push(geometry.to);
	                        for (const intersecting of this.computeWiresNetObjects([obj])) {
	                            if (!visited.has(intersecting)) {
	                                visited.add(intersecting);
	                                if (intersecting.wire) {
	                                    const intGeometry = this.getEffectiveWireGeometry(intersecting);
	                                    if (intGeometry) {
	                                        positionQueue.push(intGeometry.from);
	                                        positionQueue.push(intGeometry.to);
	                                    }
	                                }
	                            }
	                        }
	                    }
	                }
	            }
	        }
	        return visited;
	    }
	    removeObject(obj) {
	        for (const [x, yMap] of this.points.entries()) {
	            for (const [y, objects] of yMap.entries()) {
	                const idx = objects.indexOf(obj);
	                if (idx !== -1) {
	                    objects.splice(idx, 1);
	                    if (objects.length === 0) {
	                        yMap.delete(y);
	                    }
	                }
	            }
	            if (yMap.size === 0) {
	                this.points.delete(x);
	            }
	        }
	        this.removeWireFromGrid(obj);
	        this.wireObjects.delete(obj);
	        for (const [, entry] of this.wireObjects.entries()) {
	            const idx = entry.intersecting.indexOf(obj);
	            if (idx !== -1) {
	                entry.intersecting.splice(idx, 1);
	            }
	        }
	    }
	    getConnectedWireGroups() {
	        const visited = new Set();
	        const groups = new Set();
	        for (const wireObj of this.wireObjects.keys()) {
	            if (visited.has(wireObj)) {
	                continue;
	            }
	            const localEndpoint = wireObj.endpoints.find((ep) => ep.dimension === this.name);
	            const startPos = localEndpoint?.position ?? wireObj.wire.from;
	            const allConnected = this.getObjectsConnectedToPosition(startPos);
	            const wireGroup = new Set();
	            for (const obj of allConnected) {
	                if (obj.wire) {
	                    wireGroup.add(obj);
	                    visited.add(obj);
	                }
	            }
	            if (wireGroup.size > 0) {
	                groups.add(wireGroup);
	            }
	        }
	        return groups;
	    }
	    getWireIntersecting(obj) {
	        return this.wireObjects.get(obj)?.intersecting;
	    }
	    *iterateWireObjects() {
	        yield* this.wireObjects.keys();
	    }
	    *iterateLabelObjects(labelType) {
	        for (const obj of this.iterateAllObjects()) {
	            if (obj.label && (!labelType || obj.label.labelType === labelType)) {
	                yield obj;
	            }
	        }
	    }
	    *iterateConnectionObjects() {
	        for (const obj of this.iterateAllObjects()) {
	            if (obj.connection) {
	                yield obj;
	            }
	        }
	    }
	    *iterateAllObjects() {
	        const seen = new Set();
	        for (const [, yMap] of this.points.entries()) {
	            for (const [, objects] of yMap.entries()) {
	                for (const obj of objects) {
	                    if (!seen.has(obj)) {
	                        seen.add(obj);
	                        yield obj;
	                    }
	                }
	            }
	        }
	    }
	    get allPoints() {
	        return this.points;
	    }
	    roundPoint(point) {
	        return (0, Transform_1.ceilToMultiple)(parseFloat(parseFloat(point.toFixed(3)).toFixed(2)), this.pointRoundingPrecision);
	    }
	    getEffectiveWireGeometry(obj) {
	        const entry = this.wireObjects.get(obj);
	        if (entry) {
	            return entry.geometry;
	        }
	        if (!obj.wire) {
	            return null;
	        }
	        return this.getWirePositions(obj.wire);
	    }
	    computeWiresNetObjects(wires, onPos) {
	        const wireNetObjs = new Set();
	        for (const obj of wires) {
	            if (!obj.wire) {
	                continue;
	            }
	            const wirePositions = this.getEffectiveWireGeometry(obj);
	            if (!wirePositions) {
	                continue;
	            }
	            if (onPos && !this.doesWireIntersect(wirePositions, onPos)) {
	                continue;
	            }
	            wireNetObjs.add(obj);
	            for (const [x, ys] of this.points.entries()) {
	                if (Math.min(wirePositions.from[0], wirePositions.to[0]) > x + this.boundingMargin ||
	                    Math.max(wirePositions.from[0], wirePositions.to[0]) < x - this.boundingMargin) {
	                    continue;
	                }
	                for (const point of this.detectWireIntersections(wirePositions, Array.from(ys.keys()).map((y) => [x, y]))) {
	                    for (const obj of ys.get(point[1]) ?? []) {
	                        wireNetObjs.add(obj);
	                    }
	                }
	            }
	        }
	        return wireNetObjs;
	    }
	    getWirePositions(wire) {
	        const from = wire.from;
	        const to = wire.to;
	        return { from, to };
	    }
	    doesWireIntersect(wire, position) {
	        for (const _ of this.detectWireIntersections(wire, [position])) {
	            return true;
	        }
	        return false;
	    }
	    *detectWireIntersections(wire, positions) {
	        const minX = Math.min(wire.from[0], wire.to[0]);
	        const minY = Math.min(wire.from[1], wire.to[1]);
	        const maxX = Math.max(wire.from[0], wire.to[0]);
	        const maxY = Math.max(wire.from[1], wire.to[1]);
	        if (this.explicitWireIntersections) {
	            for (const pos of positions) {
	                const dxFrom = wire.from[0] - pos[0];
	                const dyFrom = wire.from[1] - pos[1];
	                if (Math.sqrt(dxFrom * dxFrom + dyFrom * dyFrom) <= this.connectionTolerance) {
	                    yield pos;
	                }
	                const dxTo = wire.to[0] - pos[0];
	                const dyTo = wire.to[1] - pos[1];
	                if (Math.sqrt(dxTo * dxTo + dyTo * dyTo) <= this.connectionTolerance) {
	                    yield pos;
	                }
	            }
	        }
	        else {
	            for (const pos of positions) {
	                if (!this.pointInRect(wire.from, wire.to, pos, minX, minY, maxX, maxY)) {
	                    continue;
	                }
	                const dx = pos[0] - wire.from[0];
	                const dy = pos[1] - wire.from[1];
	                const dot = dx * (wire.to[0] - wire.from[0]) + dy * (wire.to[1] - wire.from[1]);
	                const len_sq = (wire.to[0] - wire.from[0]) * (wire.to[0] - wire.from[0]) +
	                    (wire.to[1] - wire.from[1]) * (wire.to[1] - wire.from[1]);
	                const param = dot / len_sq;
	                const xx = wire.from[0] + param * (wire.to[0] - wire.from[0]);
	                const yy = wire.from[1] + param * (wire.to[1] - wire.from[1]);
	                const dx2 = xx - pos[0];
	                const dy2 = yy - pos[1];
	                const dist = Math.sqrt(dx2 * dx2 + dy2 * dy2);
	                if (dist <= this.connectionTolerance) {
	                    yield pos;
	                }
	            }
	        }
	    }
	    pointInRect(from, to, pos, minX = Math.min(from[0], to[0]), minY = Math.min(from[1], to[1]), maxX = Math.max(from[0], to[0]), maxY = Math.max(from[1], to[1])) {
	        const delta = this.boundingMargin;
	        return pos[0] >= minX - delta && pos[0] <= maxX + delta && pos[1] >= minY - delta && pos[1] <= maxY + delta;
	    }
	    gridKey(cx, cy) {
	        return `${cx},${cy}`;
	    }
	    addWireToGrid(obj, geometry) {
	        const delta = this.boundingMargin;
	        const minCx = Math.floor((Math.min(geometry.from[0], geometry.to[0]) - delta) / this.wireCellSize);
	        const maxCx = Math.floor((Math.max(geometry.from[0], geometry.to[0]) + delta) / this.wireCellSize);
	        const minCy = Math.floor((Math.min(geometry.from[1], geometry.to[1]) - delta) / this.wireCellSize);
	        const maxCy = Math.floor((Math.max(geometry.from[1], geometry.to[1]) + delta) / this.wireCellSize);
	        for (let cx = minCx; cx <= maxCx; cx++) {
	            for (let cy = minCy; cy <= maxCy; cy++) {
	                const key = this.gridKey(cx, cy);
	                const cell = this.wireGrid.get(key);
	                if (!cell) {
	                    this.wireGrid.set(key, obj);
	                }
	                else if (cell instanceof Set) {
	                    cell.add(obj);
	                }
	                else if (cell !== obj) {
	                    this.wireGrid.set(key, new Set([cell, obj]));
	                }
	            }
	        }
	    }
	    removeWireFromGrid(obj) {
	        for (const [key, cell] of this.wireGrid.entries()) {
	            if (cell instanceof Set) {
	                if (!cell.delete(obj)) {
	                    continue;
	                }
	                if (cell.size === 1) {
	                    const [remaining] = cell;
	                    this.wireGrid.set(key, remaining);
	                }
	                else if (cell.size === 0) {
	                    this.wireGrid.delete(key);
	                }
	            }
	            else if (cell === obj) {
	                this.wireGrid.delete(key);
	            }
	        }
	    }
	    getWiresNearPosition(pos) {
	        const cx = Math.floor(pos[0] / this.wireCellSize);
	        const cy = Math.floor(pos[1] / this.wireCellSize);
	        const result = new Set();
	        for (let dx = -1; dx <= 1; dx++) {
	            for (let dy = -1; dy <= 1; dy++) {
	                const cell = this.wireGrid.get(this.gridKey(cx + dx, cy + dy));
	                if (cell instanceof Set) {
	                    for (const obj of cell) {
	                        result.add(obj);
	                    }
	                }
	                else if (cell) {
	                    result.add(cell);
	                }
	            }
	        }
	        return result;
	    }
	}
	CombobulationDimension.CombobulationDimension = CombobulationDimension$1;
	
	return CombobulationDimension;
}

var NetUnionFind = {};

var hasRequiredNetUnionFind;

function requireNetUnionFind () {
	if (hasRequiredNetUnionFind) return NetUnionFind;
	hasRequiredNetUnionFind = 1;
	Object.defineProperty(NetUnionFind, "__esModule", { value: true });
	NetUnionFind.NetUnionFind = void 0;
	class NetUnionFind$1 {
	    constructor() {
	        this.parent = new Map();
	        this.rank = new Map();
	    }
	    makeSet(item) {
	        if (this.parent.has(item)) {
	            return;
	        }
	        this.parent.set(item, item);
	        this.rank.set(item, 0);
	    }
	    find(item) {
	        let root = this.parent.get(item);
	        if (root === undefined) {
	            throw new Error('Item not found in union-find');
	        }
	        while (this.parent.get(root) !== root) {
	            root = this.parent.get(root);
	        }
	        let current = item;
	        while (current !== root) {
	            const next = this.parent.get(current);
	            this.parent.set(current, root);
	            current = next;
	        }
	        return root;
	    }
	    union(a, b) {
	        const rootA = this.find(a);
	        const rootB = this.find(b);
	        if (rootA === rootB) {
	            return rootA;
	        }
	        const rankA = this.rank.get(rootA);
	        const rankB = this.rank.get(rootB);
	        if (rankA < rankB) {
	            this.parent.set(rootA, rootB);
	            return rootB;
	        }
	        else if (rankA > rankB) {
	            this.parent.set(rootB, rootA);
	            return rootA;
	        }
	        else {
	            this.parent.set(rootB, rootA);
	            this.rank.set(rootA, rankA + 1);
	            return rootA;
	        }
	    }
	    connected(a, b) {
	        return this.find(a) === this.find(b);
	    }
	    has(item) {
	        return this.parent.has(item);
	    }
	    getComponents() {
	        const components = new Map();
	        for (const item of this.parent.keys()) {
	            const root = this.find(item);
	            let component = components.get(root);
	            if (!component) {
	                component = [];
	                components.set(root, component);
	            }
	            component.push(item);
	        }
	        return components;
	    }
	    clear() {
	        this.parent.clear();
	        this.rank.clear();
	    }
	    get size() {
	        return this.parent.size;
	    }
	}
	NetUnionFind.NetUnionFind = NetUnionFind$1;
	
	return NetUnionFind;
}

var hasRequiredNetCombobulator;

function requireNetCombobulator () {
	if (hasRequiredNetCombobulator) return NetCombobulator;
	hasRequiredNetCombobulator = 1;
	Object.defineProperty(NetCombobulator, "__esModule", { value: true });
	NetCombobulator.NetCombobulator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const CombobulationDimension_1 = requireCombobulationDimension();
	const NetUnionFind_1 = requireNetUnionFind();
	class NetCombobulator$1 {
	    static { this.DEFAULT_DOMAIN = 'wire'; }
	    static { this.PRINT_ENTRIES = false; }
	    constructor(netRefCtx, namingStrategy, instance, opts = {}) {
	        this.netRefCtx = netRefCtx;
	        this.namingStrategy = namingStrategy;
	        this.instance = instance;
	        this.opts = opts;
	        this.dimensions = new Map();
	        this._nets = new Map();
	        this.unionFind = new NetUnionFind_1.NetUnionFind();
	        this.computedNets = false;
	        this.defaultDimension = new CombobulationDimension_1.CombobulationDimension(opts.defaultDimension ?? NetCombobulator$1.DEFAULT_DOMAIN, {
	            explicitWireIntersections: opts.explicitWireIntersections,
	            pointRoundingPrecision: opts.pointRoundingPrecision ?? 0.1,
	            connectionTolerance: opts.connectionTolerance,
	        });
	    }
	    get pointRoundingPrecision() {
	        return this.defaultDimension.pointRoundingPrecision;
	    }
	    get explicitWireIntersections() {
	        return this.opts.explicitWireIntersections ?? false;
	    }
	    *iterateNets() {
	        yield* this._nets.values();
	    }
	    *iterateWireObjects() {
	        const seen = new Set();
	        for (const dimension of this.allDimensions()) {
	            for (const obj of dimension.iterateWireObjects()) {
	                if (!seen.has(obj)) {
	                    seen.add(obj);
	                    yield obj;
	                }
	            }
	        }
	    }
	    *iterateLabelObjects(labelType) {
	        const seen = new Set();
	        for (const dimension of this.allDimensions()) {
	            for (const obj of dimension.iterateLabelObjects(labelType)) {
	                if (!seen.has(obj)) {
	                    seen.add(obj);
	                    yield obj;
	                }
	            }
	        }
	    }
	    *iterateConnectionObjects() {
	        const seen = new Set();
	        for (const dimension of this.allDimensions()) {
	            for (const obj of dimension.iterateConnectionObjects()) {
	                if (!seen.has(obj)) {
	                    seen.add(obj);
	                    yield obj;
	                }
	            }
	        }
	    }
	    *iterateAllObjects() {
	        const seen = new Set();
	        for (const dimension of this.allDimensions()) {
	            for (const obj of dimension.iterateAllObjects()) {
	                if (!seen.has(obj)) {
	                    seen.add(obj);
	                    yield obj;
	                }
	            }
	        }
	    }
	    *allDimensions() {
	        yield this.defaultDimension;
	        for (const dimension of this.dimensions.values()) {
	            yield dimension;
	        }
	    }
	    hasDimension(dimensionName) {
	        return this.dimensions.has(dimensionName);
	    }
	    getDimension(dimensionName) {
	        if (dimensionName === this.defaultDimension.name) {
	            return this.defaultDimension;
	        }
	        const dimension = this.dimensions.get(dimensionName);
	        if (!dimension) {
	            throw new Error(`Dimension ${dimensionName} not found`);
	        }
	        return dimension;
	    }
	    getOrCreateDimension(dimensionName) {
	        if (dimensionName === this.defaultDimension.name) {
	            return this.defaultDimension;
	        }
	        if (!this.dimensions.has(dimensionName)) {
	            this.dimensions.set(dimensionName, new CombobulationDimension_1.CombobulationDimension(dimensionName, {
	                explicitWireIntersections: this.opts.explicitWireIntersections,
	                pointRoundingPrecision: this.defaultDimension.pointRoundingPrecision,
	                connectionTolerance: this.defaultDimension.connectionTolerance,
	            }));
	        }
	        return this.dimensions.get(dimensionName);
	    }
	    addWire(wire) {
	        const fromEndpoint = NetCombobulator$1.toEndpoint(wire.from, this.defaultDimension.name);
	        const toEndpoint = NetCombobulator$1.toEndpoint(wire.to, this.defaultDimension.name);
	        const internalWire = {
	            from: fromEndpoint.position,
	            to: toEndpoint.position,
	            dimensions: Array.from(new Set([fromEndpoint.dimension, toEndpoint.dimension])),
	            strategyMetadata: wire.strategyMetadata,
	        };
	        const endpoints = [fromEndpoint, toEndpoint];
	        const netRef = this.getUniqueNetRef();
	        const obj = {
	            wire: internalWire,
	            netRef: netRef,
	            endpoints,
	        };
	        const transformedName = this.namingStrategy.transformNetName(obj, this.instance);
	        const connectivityName = this.namingStrategy.transformConnectivityName?.(obj, this.instance) ?? transformedName;
	        if (connectivityName) {
	            netRef.setName(connectivityName);
	            obj.appliesNetName = connectivityName;
	        }
	        this.addEndpoints(endpoints, obj);
	        this.getOrCreateDimension(fromEndpoint.dimension).addWireObject(obj);
	        if (fromEndpoint.dimension !== toEndpoint.dimension) {
	            this.getOrCreateDimension(toEndpoint.dimension).addWireObject(obj);
	        }
	        this.autoAssignNet(obj);
	        return netRef;
	    }
	    setLabel(positionOrEndpoints, originalLabel, type, strategyMetadata, options) {
	        const endpoints = NetCombobulator$1.normalizeEndpoints(positionOrEndpoints);
	        const position = endpoints[0].position;
	        const typeCanName = this.namingStrategy.canLabelTypeNameNets(type, this.instance);
	        const canNameNet = (options?.canNameNet ?? true) && typeCanName;
	        const transformedLabel = this.getUniqueNetRef(canNameNet ? originalLabel : undefined);
	        const label = {
	            originalLabel,
	            position,
	            labelType: type,
	            canNameNet: typeCanName,
	            strategyMetadata,
	        };
	        const netObject = {
	            netRef: transformedLabel,
	            endpoints,
	            label,
	        };
	        if (typeCanName) {
	            const transformedName = this.namingStrategy.transformNetName(netObject, this.instance);
	            if (canNameNet) {
	                const connectivityName = options?.connectivityName ??
	                    this.namingStrategy.transformConnectivityName?.(netObject, this.instance) ??
	                    transformedName ??
	                    originalLabel;
	                netObject.appliesNetName = connectivityName;
	                transformedLabel.setName(connectivityName);
	            }
	            label.canNameNet = canNameNet;
	            label.formattedLabel = transformedName;
	        }
	        this.addEndpoints(endpoints, netObject);
	        this.autoAssignNet(netObject);
	        return transformedLabel;
	    }
	    addConnection(positionOrEndpoints, connection) {
	        let endpoints = NetCombobulator$1.normalizeEndpoints(positionOrEndpoints);
	        const position = endpoints[0].position;
	        let netRef = this.getUniqueNetRef(connection.appliesNetName?.label);
	        if (connection.skipPositionalCombobulation) {
	            endpoints = [
	                {
	                    position,
	                    dimension: `isolated::${connection.moduleRef}::${connection.pinRef}::${netRef.valueOf()}`,
	                },
	            ];
	        }
	        let appliesNetName;
	        if (connection.appliesNetName) {
	            const canLabelNameNets = this.namingStrategy.canLabelTypeNameNets(connection.appliesNetName?.labelType, this.instance);
	            if (canLabelNameNets) {
	                appliesNetName = connection.appliesNetName;
	            }
	        }
	        const fullConnection = {
	            netRef: netRef,
	            endpoints,
	            connection: { connection: { ...connection, appliesNetName }, position },
	        };
	        const transformedName = this.namingStrategy.transformNetName(fullConnection, this.instance);
	        const connectivityName = this.namingStrategy.transformConnectivityName?.(fullConnection, this.instance) ?? transformedName;
	        if (connection.appliesNetName && !connectivityName) {
	            netRef = new sigma_engine_shared_1.NetRef(undefined, netRef.valueOf());
	            fullConnection.netRef = netRef;
	        }
	        if (connectivityName) {
	            netRef.setName(connectivityName);
	            fullConnection.appliesNetName = connectivityName;
	        }
	        this.addEndpoints(endpoints, fullConnection);
	        this.autoAssignNet(fullConnection);
	        return netRef;
	    }
	    getObjsAtPosition(pos, filterDimension) {
	        const results = new Set();
	        for (const dimension of this.allDimensions()) {
	            if (filterDimension && dimension.name !== filterDimension) {
	                continue;
	            }
	            for (const obj of dimension.getObjectsAtPosition(pos)) {
	                results.add(obj);
	            }
	        }
	        return Array.from(results);
	    }
	    getConnectedWireGroups() {
	        const objToGroup = new Map();
	        for (const dimension of this.allDimensions()) {
	            for (const group of dimension.getConnectedWireGroups()) {
	                for (const obj of group) {
	                    const existingGroup = objToGroup.get(obj);
	                    if (existingGroup && existingGroup !== group) {
	                        for (const mergedObj of group) {
	                            existingGroup.add(mergedObj);
	                            objToGroup.set(mergedObj, existingGroup);
	                        }
	                    }
	                    else {
	                        objToGroup.set(obj, group);
	                    }
	                }
	            }
	        }
	        return new Set(objToGroup.values());
	    }
	    removeObject(obj) {
	        for (const endpoint of obj.endpoints) {
	            this.getOrCreateDimension(endpoint.dimension).removeObject(obj);
	        }
	        const net = obj.net;
	        if (net) {
	            const removeFrom = (arr, item) => {
	                const idx = arr.indexOf(item);
	                if (idx !== -1) {
	                    arr.splice(idx, 1);
	                }
	            };
	            removeFrom(net.netObjects, obj);
	            if (obj.label) {
	                removeFrom(net.labels, obj.label);
	            }
	            if (obj.wire) {
	                removeFrom(net.wires, obj.wire);
	            }
	            if (obj.connection) {
	                removeFrom(net.connections, obj.connection);
	            }
	            net.netRefs.delete(obj.netRef);
	            if (net.netObjects.length === 0) {
	                for (const [name, n] of this._nets.entries()) {
	                    if (n === net) {
	                        this._nets.delete(name);
	                    }
	                }
	            }
	            obj.net = undefined;
	        }
	    }
	    mergeNets(a, b) {
	        if (!b || a === b) {
	            return a;
	        }
	        for (const ref of b.netRefs) {
	            a.netRefs.add(ref);
	        }
	        if (b.wires.length > 0) {
	            const wireSet = (a._wireSet ??= new Set(a.wires));
	            for (const w of b.wires) {
	                if (!wireSet.has(w)) {
	                    wireSet.add(w);
	                    a.wires.push(w);
	                }
	            }
	        }
	        if (b.labels.length > 0) {
	            const labelSet = (a._labelSet ??= new Set(a.labels));
	            for (const l of b.labels) {
	                if (!labelSet.has(l)) {
	                    labelSet.add(l);
	                    a.labels.push(l);
	                }
	            }
	        }
	        if (b.connections.length > 0) {
	            const connKeySet = (a._connKeySet ??= new Set(a.connections.map((c) => `${c.connection.moduleRef}\0${c.connection.pinRef}`)));
	            for (const c of b.connections) {
	                const key = `${c.connection.moduleRef}\0${c.connection.pinRef}`;
	                if (!connKeySet.has(key)) {
	                    connKeySet.add(key);
	                    a.connections.push(c);
	                }
	            }
	        }
	        if (b.netObjects.length > 0) {
	            const objSet = (a._objSet ??= new Set(a.netObjects));
	            for (const o of b.netObjects) {
	                if (!objSet.has(o)) {
	                    objSet.add(o);
	                    a.netObjects.push(o);
	                }
	            }
	        }
	        for (const [name, currentNet] of this._nets.entries()) {
	            if (currentNet === b) {
	                this._nets.set(name, a);
	            }
	        }
	        for (const obj of b.netObjects) {
	            obj.net = a;
	        }
	        b._objSet = undefined;
	        b._wireSet = undefined;
	        b._labelSet = undefined;
	        b._connKeySet = undefined;
	        return a;
	    }
	    static normalizeEndpoints(positionOrEndpoints) {
	        if (Array.isArray(positionOrEndpoints) &&
	            positionOrEndpoints.length > 0 &&
	            typeof positionOrEndpoints[0] === 'object' &&
	            !Array.isArray(positionOrEndpoints[0])) {
	            return positionOrEndpoints;
	        }
	        return [{ position: positionOrEndpoints, dimension: NetCombobulator$1.DEFAULT_DOMAIN }];
	    }
	    getUniqueNetRef(initialName) {
	        let name = initialName;
	        if (!name) {
	            name = `__UNNAMED_NET_${this.netRefCtx.getUniqueRefNo()}`;
	        }
	        return new sigma_engine_shared_1.NetRef(this.netRefCtx, name);
	    }
	    static toEndpoint(positionOrEndpoint, defaultDimension) {
	        if (Array.isArray(positionOrEndpoint)) {
	            return { position: positionOrEndpoint, dimension: defaultDimension };
	        }
	        return positionOrEndpoint;
	    }
	    addEndpoints(endpoints, obj) {
	        for (const endpoint of endpoints) {
	            this.getOrCreateDimension(endpoint.dimension).addPoint(endpoint.position, obj);
	        }
	    }
	    autoAssignNet(obj) {
	        if (!this.opts.autoAssignNets) {
	            return;
	        }
	        const net = {
	            name: this.getUniqueNetRef(),
	            netRefs: new Set(),
	            labels: [],
	            wires: [],
	            connections: [],
	            netObjects: [],
	            instance: this.instance,
	        };
	        this.collectObjectIntoNet(net, obj);
	        this._nets.set(obj.netRef.valueOf(), net);
	    }
	    collectObjectIntoNet(net, obj) {
	        obj.net = net;
	        const objSet = (net._objSet ??= new Set(net.netObjects));
	        if (obj.label) {
	            const labelSet = (net._labelSet ??= new Set(net.labels));
	            if (!labelSet.has(obj.label)) {
	                labelSet.add(obj.label);
	                net.labels.push(obj.label);
	            }
	        }
	        if (!objSet.has(obj)) {
	            objSet.add(obj);
	            net.netObjects.push(obj);
	        }
	        const objNetRef = obj.netRef;
	        if (objNetRef && !net.netRefs.has(objNetRef)) {
	            net.netRefs.add(objNetRef);
	        }
	        if (obj.connection) {
	            const connKeySet = (net._connKeySet ??= new Set(net.connections.map((c) => `${c.connection.moduleRef}\0${c.connection.pinRef}`)));
	            const connKey = `${obj.connection.connection.moduleRef}\0${obj.connection.connection.pinRef}`;
	            if (!connKeySet.has(connKey)) {
	                connKeySet.add(connKey);
	                net.connections.push(obj.connection);
	            }
	        }
	        if (obj.wire) {
	            const wireSet = (net._wireSet ??= new Set(net.wires));
	            if (!wireSet.has(obj.wire)) {
	                wireSet.add(obj.wire);
	                net.wires.push(obj.wire);
	            }
	        }
	    }
	    getFinalizedNets() {
	        if (!this.finalizedNets) {
	            throw new Error('Nets not finalized yet');
	        }
	        return this.finalizedNets;
	    }
	    computeNets() {
	        this._nets = new Map();
	        this.rebuildUnionFind();
	        const components = this.unionFind.getComponents();
	        this.unionFind.clear();
	        const labelNets = new Map();
	        for (const [, component] of components) {
	            let net = {
	                name: this.getUniqueNetRef(),
	                netRefs: new Set(),
	                labels: [],
	                wires: [],
	                connections: [],
	                netObjects: [],
	                instance: this.instance,
	            };
	            for (const obj of component) {
	                if (obj.net) {
	                    net = this.mergeNets(net, obj.net);
	                }
	            }
	            for (const obj of component) {
	                this.collectObjectIntoNet(net, obj);
	                if (obj.appliesNetName && this._nets.has(obj.appliesNetName)) {
	                    net = this.mergeNets(net, this._nets.get(obj.appliesNetName));
	                }
	                if (obj.appliesNetName) {
	                    const existingNet = labelNets.get(obj.appliesNetName);
	                    if (existingNet) {
	                        net = this.mergeNets(net, existingNet);
	                    }
	                    labelNets.set(obj.appliesNetName, net);
	                }
	            }
	            net = this.addNet(net);
	            for (const obj of component) {
	                obj.net = net;
	            }
	        }
	        this.finalizedNets = new Map();
	        for (const net of this._nets.values()) {
	            const name = this.getNetName(net);
	            let finalNet = {
	                ...net,
	                name: this.netRefCtx.existingOrNewRef(name),
	            };
	            finalNet.netRefs.add(finalNet.name);
	            if (this.finalizedNets.has(finalNet.name.valueOf())) {
	                finalNet = this.mergeNets(finalNet, this.finalizedNets.get(finalNet.name.valueOf()));
	            }
	            for (const netRef of finalNet.netRefs) {
	                netRef.setName(finalNet.name);
	            }
	            for (const obj of finalNet.netObjects) {
	                obj.netRef.setName(finalNet.name);
	                obj.net = finalNet;
	            }
	            this.finalizedNets.set(finalNet.name.valueOf(), finalNet);
	        }
	        this._nets = this.finalizedNets;
	        this.computedNets = true;
	        const finalizedNetSet = new Set(this.finalizedNets.values());
	        for (const dimension of this.allDimensions()) {
	            for (const { x, y, objects: netObjs } of dimension.iteratePointGroups()) {
	                const uniqueNets = new Set(netObjs.map((o) => o.net).filter((n) => n !== undefined));
	                if (uniqueNets.size > 1) {
	                    throw new Error(`Multiple unmerged nets at position ${x}, ${y}`);
	                }
	                for (const obj of netObjs) {
	                    if (!obj.net) {
	                        throw new Error(`No net for object at ${x}, ${y}`);
	                    }
	                    const memberSet = obj.net._objSet;
	                    if (memberSet ? !memberSet.has(obj) : !obj.net.netObjects.includes(obj)) {
	                        throw new Error(`Net for object at ${x}, ${y} does not include object`);
	                    }
	                    if (!finalizedNetSet.has(obj.net)) {
	                        throw new Error(`Net for object at ${x}, ${y} is not in the final net list`);
	                    }
	                }
	            }
	        }
	        return this.finalizedNets;
	    }
	    rebuildUnionFind() {
	        this.unionFind.clear();
	        for (const dimension of this.allDimensions()) {
	            for (const { objects } of dimension.iteratePointGroups()) {
	                if (objects.length === 0) {
	                    continue;
	                }
	                this.unionFind.makeSet(objects[0]);
	                for (let i = 1; i < objects.length; i++) {
	                    this.unionFind.makeSet(objects[i]);
	                    this.unionFind.union(objects[0], objects[i]);
	                }
	            }
	            for (const wireObj of dimension.iterateWireObjects()) {
	                this.unionFind.makeSet(wireObj);
	                const intersecting = dimension.getWireIntersecting(wireObj);
	                if (intersecting) {
	                    for (const obj of intersecting) {
	                        this.unionFind.makeSet(obj);
	                        this.unionFind.union(wireObj, obj);
	                    }
	                }
	                const geometry = dimension.getEffectiveWireGeometry(wireObj);
	                if (geometry) {
	                    for (const pos of [geometry.from, geometry.to]) {
	                        for (const obj of dimension.getObjectsAtPosition(pos)) {
	                            this.unionFind.makeSet(obj);
	                            this.unionFind.union(wireObj, obj);
	                        }
	                    }
	                }
	            }
	        }
	    }
	    getNetAtPosition(pos) {
	        if (!this.computedNets) {
	            throw new Error(`Run computeNets first`);
	        }
	        const objs = this.getObjsAtPosition(pos);
	        const allNets = new Set(objs.map((o) => o.netRef?.valueOf()).filter((v) => !!v));
	        if (allNets.size > 1) {
	            throw new Error(`Multiple unmerged nets at position ${pos}`);
	        }
	        if (objs && objs.length > 0) {
	            return objs[0].net;
	        }
	        return undefined;
	    }
	    addNet(net) {
	        const name = this.getNetName(net).valueOf();
	        let fullNet = {
	            ...net,
	            name: this.netRefCtx.existingOrNewRef(name),
	        };
	        fullNet.netRefs.add(fullNet.name);
	        if (!this._nets.has(name)) {
	            this._nets.set(name, fullNet);
	        }
	        else {
	            fullNet = this.mergeNets(fullNet, this._nets.get(name));
	            this._nets.set(name, fullNet);
	        }
	        return fullNet;
	    }
	    getNetName(net) {
	        return this.namingStrategy.resolveNetName(net, this.instance);
	    }
	}
	NetCombobulator.NetCombobulator = NetCombobulator$1;
	
	return NetCombobulator;
}

var NetNamingRuleChain = {};

var NetGroupManager = {};

var hasRequiredNetGroupManager;

function requireNetGroupManager () {
	if (hasRequiredNetGroupManager) return NetGroupManager;
	hasRequiredNetGroupManager = 1;
	Object.defineProperty(NetGroupManager, "__esModule", { value: true });
	NetGroupManager.NetGroupManager = void 0;
	class NetGroupManager$1 {
	    constructor() {
	        this.groups = [];
	        this.redirects = new Map();
	        this.labelIndex = new Map();
	    }
	    resolveGroup(group) {
	        return this.resolveLiveGroup(group);
	    }
	    addGroup(group) {
	        this.redirects.delete(group);
	        this.groups.push(group);
	        this.indexGroupLabels(group);
	    }
	    indexGroupLabels(group) {
	        for (const label of group.getNetLabels()) {
	            this.indexLabel(label, group);
	        }
	    }
	    indexLabel(label, group) {
	        const value = label.formattedLabel ?? label.originalLabel;
	        let byValue = this.labelIndex.get(label.labelType);
	        if (!byValue) {
	            byValue = new Map();
	            this.labelIndex.set(label.labelType, byValue);
	        }
	        let groupSet = byValue.get(value);
	        if (!groupSet) {
	            groupSet = new Set();
	            byValue.set(value, groupSet);
	        }
	        groupSet.add(group);
	    }
	    deindexGroup(group) {
	        for (const [, byValue] of this.labelIndex) {
	            for (const [, groupSet] of byValue) {
	                groupSet.delete(group);
	            }
	        }
	    }
	    mergeGroups(...groups) {
	        const liveGroups = Array.from(new Set(groups.map((group) => this.resolveLiveGroup(group))));
	        if (liveGroups.length < 2) {
	            return;
	        }
	        const [target, ...toMerge] = liveGroups;
	        for (const group of toMerge) {
	            for (const [instance, nets] of group.getNets()) {
	                for (const net of nets) {
	                    target.merge(instance, net);
	                }
	            }
	            this.deindexGroup(group);
	            this.redirects.set(group, target);
	            const index = this.groups.indexOf(group);
	            if (index !== -1) {
	                this.groups.splice(index, 1);
	            }
	        }
	        this.indexGroupLabels(target);
	    }
	    resolveLiveGroup(group) {
	        const path = [];
	        const seen = new Set();
	        let current = group;
	        while (this.redirects.has(current)) {
	            if (seen.has(current)) {
	                const live = path.find((candidate) => this.groups.includes(candidate)) ?? current;
	                for (const stale of path) {
	                    if (stale !== live) {
	                        this.redirects.set(stale, live);
	                    }
	                }
	                this.redirects.delete(live);
	                return live;
	            }
	            seen.add(current);
	            path.push(current);
	            const next = this.redirects.get(current);
	            if (!next) {
	                break;
	            }
	            current = next;
	        }
	        for (const stale of path) {
	            if (stale !== current) {
	                this.redirects.set(stale, current);
	            }
	        }
	        return current;
	    }
	    moveNetsToGroup(target, source, nets) {
	        target = this.resolveLiveGroup(target);
	        source = this.resolveLiveGroup(source);
	        if (target === source) {
	            return;
	        }
	        this.deindexGroup(source);
	        for (const [instance, net] of nets) {
	            if (source.removeNet(instance, net)) {
	                target.merge(instance, net);
	            }
	        }
	        if (source.isEmpty()) {
	            const sourceIndex = this.groups.indexOf(source);
	            if (sourceIndex !== -1) {
	                this.groups.splice(sourceIndex, 1);
	            }
	        }
	        else {
	            this.indexGroupLabels(source);
	        }
	        this.indexGroupLabels(target);
	    }
	    *iterateGroups() {
	        yield* this.groups;
	    }
	    getGroupCount() {
	        return this.groups.length;
	    }
	    getGroupByIndex(index) {
	        return this.groups[index];
	    }
	    findGroupsWithLabel(labelType, value, searchInInstance) {
	        const byValue = this.labelIndex.get(labelType);
	        if (!byValue) {
	            return [];
	        }
	        let candidates;
	        if (value !== undefined) {
	            const groupSet = byValue.get(value);
	            if (!groupSet || groupSet.size === 0) {
	                return [];
	            }
	            candidates = new Set(Array.from(groupSet, (group) => this.resolveLiveGroup(group)));
	        }
	        else {
	            const seen = new Set();
	            for (const groupSet of byValue.values()) {
	                for (const group of groupSet) {
	                    seen.add(this.resolveLiveGroup(group));
	                }
	            }
	            candidates = seen;
	        }
	        if (!searchInInstance) {
	            return [...candidates];
	        }
	        const result = [];
	        for (const group of candidates) {
	            if (group.hasLabelType(labelType, searchInInstance)) {
	                if (value === undefined ||
	                    group
	                        .getNetLabels(searchInInstance)
	                        .some((label) => label.labelType === labelType && (label.formattedLabel ?? label.originalLabel) === value)) {
	                    result.push(group);
	                }
	            }
	        }
	        return result;
	    }
	    findGroupsWithObjectsOnDimension(dimension) {
	        const matchingGroups = [];
	        for (const group of this.groups) {
	            if (group.hasObjectsOnDimension(dimension)) {
	                matchingGroups.push(group);
	            }
	        }
	        return matchingGroups;
	    }
	}
	NetGroupManager.NetGroupManager = NetGroupManager$1;
	
	return NetGroupManager;
}

var NetNamingDebugInfo = {};

var hasRequiredNetNamingDebugInfo;

function requireNetNamingDebugInfo () {
	if (hasRequiredNetNamingDebugInfo) return NetNamingDebugInfo;
	hasRequiredNetNamingDebugInfo = 1;
	Object.defineProperty(NetNamingDebugInfo, "__esModule", { value: true });
	NetNamingDebugInfo.serializeTrackedObjectIdentifier = serializeTrackedObjectIdentifier;
	NetNamingDebugInfo.connectionIdentifiersEqual = connectionIdentifiersEqual;
	NetNamingDebugInfo.labelIdentifiersEqual = labelIdentifiersEqual;
	function serializeTrackedObjectIdentifier(identifier) {
	    if (identifier.type === 'connection') {
	        const conn = identifier.connection;
	        const moduleId = conn.moduleRef || conn.moduleName || 'unknown';
	        return `conn:${conn.instancePathId}::${moduleId}::${conn.pinRef}`;
	    }
	    else {
	        const label = identifier.label;
	        return `label:${label.instancePathId}::${label.labelType}::${label.originalLabel}::${label.position[0]},${label.position[1]}`;
	    }
	}
	function connectionIdentifiersEqual(a, b) {
	    if (a.moduleRef && b.moduleRef) {
	        return a.moduleRef === b.moduleRef && a.pinRef === b.pinRef && a.instancePathId === b.instancePathId;
	    }
	    if (a.moduleName && b.moduleName) {
	        return a.moduleName === b.moduleName && a.pinRef === b.pinRef && a.instancePathId === b.instancePathId;
	    }
	    return false;
	}
	function labelIdentifiersEqual(a, b) {
	    return (a.labelType === b.labelType &&
	        a.originalLabel === b.originalLabel &&
	        a.instancePathId === b.instancePathId &&
	        a.position[0] === b.position[0] &&
	        a.position[1] === b.position[1]);
	}
	
	return NetNamingDebugInfo;
}

var hasRequiredNetNamingRuleChain;

function requireNetNamingRuleChain () {
	if (hasRequiredNetNamingRuleChain) return NetNamingRuleChain;
	hasRequiredNetNamingRuleChain = 1;
	Object.defineProperty(NetNamingRuleChain, "__esModule", { value: true });
	NetNamingRuleChain.NetNamingRuleChain = void 0;
	const NetGroupManager_1 = requireNetGroupManager();
	const NetNamingDebugInfo_1 = requireNetNamingDebugInfo();
	const NetNamingRules_1 = requireNetNamingRules();
	class NetNamingRuleChain$1 {
	    constructor(enableDebug = false) {
	        this.enableDebug = enableDebug;
	        this.rules = [];
	        this.localNamingRules = [];
	    }
	    add(rule) {
	        if (rule.type === NetNamingRules_1.NetNamingRuleType.LocalNaming) {
	            this.localNamingRules.push(rule);
	        }
	        else {
	            this.rules.push(rule);
	        }
	        return this;
	    }
	    execute(hierarchy) {
	        let manager = new NetGroupManager_1.NetGroupManager();
	        if (this.enableDebug) {
	            this.resetDebugInfo();
	            this.takeSnapshot(manager, -1, 'initial', 'initial', hierarchy);
	        }
	        for (const rule of this.localNamingRules) {
	            rule.apply(hierarchy);
	        }
	        for (let i = 0; i < this.rules.length; i++) {
	            const rule = this.rules[i];
	            if (rule.type === NetNamingRules_1.NetNamingRuleType.Group) {
	                manager = rule.apply(manager, hierarchy);
	                if (this.debugInfo) {
	                    this.takeSnapshot(manager, i, rule.constructor.name, 'grouping', hierarchy);
	                }
	            }
	            else if (rule.type === NetNamingRules_1.NetNamingRuleType.Naming) {
	                for (const group of manager.iterateGroups()) {
	                    if (group.physicalName) {
	                        continue;
	                    }
	                    const name = rule.apply(group, hierarchy);
	                    if (name) {
	                        group.physicalName = name;
	                    }
	                }
	                if (this.debugInfo) {
	                    this.takeSnapshot(manager, i, rule.constructor.name, 'naming', hierarchy);
	                }
	            }
	            else if (rule.type === NetNamingRules_1.NetNamingRuleType.Finalize) {
	                rule.apply(Array.from(manager.iterateGroups()), hierarchy);
	                if (this.debugInfo) {
	                    this.takeSnapshot(manager, i, rule.constructor.name, 'finalize', hierarchy);
	                }
	            }
	            else if (rule.type === NetNamingRules_1.NetNamingRuleType.LocalNaming) ;
	            else {
	                throw new Error(`Unknown rule type: ${rule.type}`);
	            }
	        }
	        if (this.debugInfo) {
	            this.computeTrackedObjectHistories();
	        }
	        return Array.from(manager.iterateGroups());
	    }
	    trackConnection(moduleRef, moduleName, pinRef, instancePathId = '') {
	        if (!moduleRef && !moduleName) {
	            throw new Error('Must provide either moduleRef or moduleName');
	        }
	        this.trackConnectionInternal({
	            moduleRef,
	            moduleName,
	            pinRef,
	            instancePathId,
	        });
	        return this;
	    }
	    trackLabel(labelType, originalLabel, position, instancePathId = '') {
	        this.trackLabelInternal({
	            labelType,
	            originalLabel,
	            position,
	            instancePathId,
	        });
	        return this;
	    }
	    trackAllConnections() {
	        return this;
	    }
	    trackAllLabels() {
	        return this;
	    }
	    getConnectionHistory(moduleRef, moduleName, pinRef, instancePathId) {
	        if (!moduleRef && !moduleName) {
	            throw new Error('Must provide either moduleRef or moduleName');
	        }
	        if (!this.debugInfo) {
	            return undefined;
	        }
	        const searchAllInstances = instancePathId === null || instancePathId === undefined;
	        const searchInstancePathId = instancePathId ?? '';
	        if (moduleRef) {
	            if (searchAllInstances) {
	                for (const [_key, history] of this.debugInfo.trackedObjects) {
	                    if (history.identifier.type === 'connection' &&
	                        history.identifier.connection.moduleRef === moduleRef &&
	                        history.identifier.connection.pinRef === pinRef) {
	                        return history;
	                    }
	                }
	            }
	            else {
	                const identifier = {
	                    type: 'connection',
	                    connection: { moduleRef, pinRef, instancePathId: searchInstancePathId },
	                };
	                const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
	                const result = this.debugInfo.trackedObjects.get(key);
	                if (result) {
	                    return result;
	                }
	            }
	        }
	        if (moduleName) {
	            for (const [_key, history] of this.debugInfo.trackedObjects) {
	                if (history.identifier.type === 'connection' &&
	                    history.identifier.connection.moduleName === moduleName &&
	                    history.identifier.connection.pinRef === pinRef &&
	                    (searchAllInstances || history.identifier.connection.instancePathId === searchInstancePathId)) {
	                    return history;
	                }
	            }
	        }
	        return undefined;
	    }
	    getLabelHistory(labelType, originalLabel, position, instancePathId = '') {
	        if (!this.debugInfo) {
	            return undefined;
	        }
	        const identifier = {
	            type: 'label',
	            label: { labelType, originalLabel, position, instancePathId },
	        };
	        const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
	        return this.debugInfo.trackedObjects.get(key);
	    }
	    getConnectionPath(moduleRef, moduleName, pinRef, instancePathId) {
	        const history = this.getConnectionHistory(moduleRef, moduleName, pinRef, instancePathId);
	        if (!history) {
	            return 'Not tracked';
	        }
	        const displayName = moduleRef ?? moduleName ?? 'unknown';
	        const actualInstancePathId = history.identifier.type === 'connection' ? history.identifier.connection.instancePathId : '';
	        let path = `Connection ${displayName}-${pinRef}${actualInstancePathId ? ` (${actualInstancePathId})` : ''}:\n`;
	        for (const entry of history.history) {
	            path += `  [${entry.ruleIndex}] ${entry.ruleName}: `;
	            if (entry.event === 'created') {
	                path += `Started in group ${entry.groupId}\n`;
	            }
	            else if (entry.event === 'stayed') {
	                path += `Remained in group ${entry.groupId}\n`;
	            }
	            else if (entry.event === 'merged-into') {
	                path += `Moved from group ${entry.details?.fromGroupId} to group ${entry.groupId}`;
	                if (entry.details?.mergedWithGroupIds && entry.details.mergedWithGroupIds.length > 0) {
	                    path += ` (merged with groups ${entry.details.mergedWithGroupIds.join(', ')})`;
	                }
	                path += '\n';
	            }
	            else if (entry.event === 'named') {
	                path += `Named "${entry.details?.assignedName ?? '<unknown>'}" in group ${entry.groupId}\n`;
	            }
	        }
	        path += `  Final: Group ${history.finalGroupId}, Name: ${history.finalName ?? '<unnamed>'}`;
	        return path;
	    }
	    getLabelPath(labelType, originalLabel, position, instancePathId = '') {
	        const history = this.getLabelHistory(labelType, originalLabel, position, instancePathId);
	        if (!history) {
	            return 'Not tracked';
	        }
	        let path = `Label ${labelType}:"${originalLabel}"${instancePathId ? ` (${instancePathId})` : ''}:\n`;
	        for (const entry of history.history) {
	            path += `  [${entry.ruleIndex}] ${entry.ruleName}: `;
	            if (entry.event === 'created') {
	                path += `Started in group ${entry.groupId}\n`;
	            }
	            else if (entry.event === 'stayed') {
	                path += `Remained in group ${entry.groupId}\n`;
	            }
	            else if (entry.event === 'merged-into') {
	                path += `Moved from group ${entry.details?.fromGroupId} to group ${entry.groupId}`;
	                if (entry.details?.mergedWithGroupIds && entry.details.mergedWithGroupIds.length > 0) {
	                    path += ` (merged with groups ${entry.details.mergedWithGroupIds.join(', ')})`;
	                }
	                path += '\n';
	            }
	            else if (entry.event === 'named') {
	                path += `Named "${entry.details?.assignedName ?? 'unknown'}" in group ${entry.groupId}\n`;
	            }
	        }
	        path += `  Final: Group ${history.finalGroupId}, Name: ${history.finalName ?? 'unnamed'}`;
	        return path;
	    }
	    exportDebugInfo() {
	        return this.debugInfo;
	    }
	    getOrCreateGroupId(group) {
	        if (!this.debugInfo) {
	            throw new Error('Debug mode not enabled');
	        }
	        let groupId = this.debugInfo.groupIdMap.get(group);
	        if (groupId === undefined) {
	            groupId = this.debugInfo.groupIdCounter++;
	            this.debugInfo.groupIdMap.set(group, groupId);
	        }
	        return groupId;
	    }
	    trackConnectionInternal(connection) {
	        if (!this.debugInfo) {
	            return;
	        }
	        const identifier = {
	            type: 'connection',
	            connection,
	        };
	        const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
	        if (!this.debugInfo.trackedObjects.has(key)) {
	            this.debugInfo.trackedObjects.set(key, {
	                identifier,
	                history: [],
	                finalGroupId: -1,
	            });
	        }
	    }
	    trackLabelInternal(label) {
	        if (!this.debugInfo) {
	            return;
	        }
	        const identifier = {
	            type: 'label',
	            label,
	        };
	        const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
	        if (!this.debugInfo.trackedObjects.has(key)) {
	            this.debugInfo.trackedObjects.set(key, {
	                identifier,
	                history: [],
	                finalGroupId: -1,
	            });
	        }
	    }
	    createGroupSnapshot(group, hierarchy) {
	        if (!this.debugInfo) {
	            throw new Error('Debug mode not enabled');
	        }
	        const connections = [];
	        const labels = [];
	        let netCount = 0;
	        for (const [instance, nets] of group.getNets()) {
	            netCount += nets.length;
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    connections.push({
	                        moduleRef: conn.connection.moduleRef,
	                        moduleName: conn.connection.moduleName,
	                        pinRef: conn.connection.pinRef,
	                        instancePathId: this.getInstancePathId(instance, hierarchy),
	                    });
	                }
	                for (const label of net.labels) {
	                    labels.push({
	                        labelType: label.labelType,
	                        originalLabel: label.originalLabel,
	                        position: label.position,
	                        instancePathId: this.getInstancePathId(instance, hierarchy),
	                    });
	                }
	            }
	        }
	        return {
	            groupId: this.getOrCreateGroupId(group),
	            netCount,
	            physicalName: group.physicalName ? group.physicalName : undefined,
	            connections,
	            labels,
	        };
	    }
	    getInstancePathId(instance, hierarchy) {
	        if (hierarchy.instance === instance) {
	            return hierarchy.pathUniqueId;
	        }
	        for (const child of hierarchy.children) {
	            const result = this.getInstancePathId(instance, child);
	            if (result !== '') {
	                return result;
	            }
	        }
	        return '';
	    }
	    createManagerSnapshot(manager, ruleIndex, ruleName, ruleType, hierarchy) {
	        if (!this.debugInfo) {
	            throw new Error('Debug mode not enabled');
	        }
	        const groups = [];
	        for (const group of manager.iterateGroups()) {
	            groups.push(this.createGroupSnapshot(group, hierarchy));
	        }
	        return {
	            ruleIndex,
	            ruleName,
	            ruleType,
	            groups,
	            timestamp: Date.now(),
	        };
	    }
	    takeSnapshot(manager, ruleIndex, ruleName, ruleType, hierarchy) {
	        if (!this.debugInfo) {
	            return;
	        }
	        const snapshot = this.createManagerSnapshot(manager, ruleIndex, ruleName, ruleType, hierarchy);
	        this.debugInfo.snapshots.push(snapshot);
	    }
	    findGroupContaining(snapshot, identifier) {
	        for (const group of snapshot.groups) {
	            if (identifier.type === 'connection') {
	                const conn = identifier.connection;
	                const found = group.connections.some((c) => {
	                    if (conn.moduleRef && c.moduleRef === conn.moduleRef) {
	                        return c.pinRef === conn.pinRef && c.instancePathId === conn.instancePathId;
	                    }
	                    if (conn.moduleName && c.moduleName === conn.moduleName) {
	                        return c.pinRef === conn.pinRef && c.instancePathId === conn.instancePathId;
	                    }
	                    return false;
	                });
	                if (found) {
	                    return group.groupId;
	                }
	            }
	            else {
	                const label = identifier.label;
	                const found = group.labels.some((l) => l.labelType === label.labelType &&
	                    l.originalLabel === label.originalLabel &&
	                    l.instancePathId === label.instancePathId &&
	                    l.position[0] === label.position[0] &&
	                    l.position[1] === label.position[1]);
	                if (found) {
	                    return group.groupId;
	                }
	            }
	        }
	        return undefined;
	    }
	    determineEvent(previousGroupId, currentGroupId, snapshot) {
	        if (previousGroupId === undefined) {
	            return 'created';
	        }
	        if (previousGroupId === currentGroupId) {
	            return snapshot.ruleType === 'naming' ? 'named' : 'stayed';
	        }
	        return 'merged-into';
	    }
	    findMergedGroups(previousSnapshot, currentSnapshot, targetGroupId) {
	        const mergedGroupIds = [];
	        const targetGroup = currentSnapshot.groups.find((g) => g.groupId === targetGroupId);
	        if (!targetGroup) {
	            return [];
	        }
	        for (const prevGroup of previousSnapshot.groups) {
	            if (prevGroup.groupId === targetGroupId) {
	                continue;
	            }
	            const hasSharedConnection = prevGroup.connections.some((conn) => targetGroup.connections.some((tc) => tc.moduleRef === conn.moduleRef && tc.pinRef === conn.pinRef && tc.instancePathId === conn.instancePathId));
	            if (hasSharedConnection) {
	                mergedGroupIds.push(prevGroup.groupId);
	            }
	        }
	        return mergedGroupIds;
	    }
	    computeTrackedObjectHistories() {
	        if (!this.debugInfo) {
	            return;
	        }
	        if (this.debugInfo.trackedObjects.size === 0) {
	            const firstNonEmptySnapshot = this.debugInfo.snapshots.find((s) => s.groups.length > 0);
	            if (firstNonEmptySnapshot) {
	                for (const group of firstNonEmptySnapshot.groups) {
	                    for (const conn of group.connections) {
	                        this.trackConnectionInternal(conn);
	                    }
	                    for (const label of group.labels) {
	                        this.trackLabelInternal(label);
	                    }
	                }
	            }
	        }
	        for (const [_key, history] of this.debugInfo.trackedObjects) {
	            const identifier = history.identifier;
	            let previousGroupId;
	            for (const snapshot of this.debugInfo.snapshots) {
	                const currentGroupId = this.findGroupContaining(snapshot, identifier);
	                if (currentGroupId === undefined) {
	                    continue;
	                }
	                const entry = {
	                    ruleIndex: snapshot.ruleIndex,
	                    ruleName: snapshot.ruleName,
	                    ruleType: snapshot.ruleType,
	                    groupId: currentGroupId,
	                    event: this.determineEvent(previousGroupId, currentGroupId, snapshot),
	                };
	                if (entry.event === 'merged-into' && previousGroupId !== undefined) {
	                    const previousSnapshot = this.debugInfo.snapshots.find((s) => s.ruleIndex === snapshot.ruleIndex - 1);
	                    if (previousSnapshot) {
	                        entry.details = {
	                            fromGroupId: previousGroupId,
	                            mergedWithGroupIds: this.findMergedGroups(previousSnapshot, snapshot, currentGroupId),
	                        };
	                    }
	                }
	                if (entry.event === 'named') {
	                    const currentGroup = snapshot.groups.find((g) => g.groupId === currentGroupId);
	                    if (currentGroup) {
	                        entry.details = {
	                            assignedName: currentGroup.physicalName,
	                        };
	                    }
	                }
	                history.history.push(entry);
	                previousGroupId = currentGroupId;
	            }
	            history.finalGroupId = previousGroupId ?? -1;
	            if (previousGroupId !== undefined && this.debugInfo.snapshots.length > 0) {
	                const lastSnapshot = this.debugInfo.snapshots[this.debugInfo.snapshots.length - 1];
	                const finalGroup = lastSnapshot.groups.find((g) => g.groupId === previousGroupId);
	                if (finalGroup) {
	                    history.finalName = finalGroup.physicalName;
	                }
	            }
	        }
	    }
	    resetDebugInfo() {
	        this.debugInfo = {
	            enabled: true,
	            snapshots: [],
	            trackedObjects: new Map(),
	            groupIdMap: new WeakMap(),
	            groupIdCounter: 0,
	        };
	    }
	}
	NetNamingRuleChain.NetNamingRuleChain = NetNamingRuleChain$1;
	
	return NetNamingRuleChain;
}

var hasRequiredKicadNetNamingStrategy;

function requireKicadNetNamingStrategy () {
	if (hasRequiredKicadNetNamingStrategy) return KicadNetNamingStrategy;
	hasRequiredKicadNetNamingStrategy = 1;
	Object.defineProperty(KicadNetNamingStrategy, "__esModule", { value: true });
	KicadNetNamingStrategy.KicadNetNamingStrategy = KicadNetNamingStrategy.KICAD_PIN_ELECTRICAL_TYPE_KEY = void 0;
	KicadNetNamingStrategy.kicadUnitSubReference = kicadUnitSubReference;
	const sigma_engine_shared_1 = requireDist$1();
	const IndividualGroupingRule_1 = requireIndividualGroupingRule();
	const NetCombobulator_1 = requireNetCombobulator();
	const NetNamingRuleChain_1 = requireNetNamingRuleChain();
	KicadNetNamingStrategy.KICAD_PIN_ELECTRICAL_TYPE_KEY = 'kicad.electricalType';
	function kicadUnitSubReference(unit) {
	    let remaining = Math.floor(unit);
	    let result = '';
	    while (remaining > 0) {
	        remaining--;
	        result = String.fromCharCode('A'.charCodeAt(0) + (remaining % 26)) + result;
	        remaining = Math.floor(remaining / 26);
	    }
	    return result;
	}
	class KicadNetNamingStrategy$1 {
	    constructor(sheetPath = '/', globalDriverNames = new Set()) {
	        this.sheetPath = sheetPath;
	        this.globalDriverNames = globalDriverNames;
	        this.netNo = 0;
	        this.implicitNetNames = new Map();
	        this.unconnectedPinNetNames = new Map();
	        this.pinHintNames = new Map();
	        this.noConnectMarkers = [];
	    }
	    transformNetName(netObject) {
	        if (netObject.label?.canNameNet) {
	            return netObject.label.formattedLabel ?? netObject.label.originalLabel;
	        }
	        if (netObject.connection?.connection.appliesNetName?.label &&
	            netObject.connection.connection.appliesNetName.labelType !== 'pin_hint') {
	            return netObject.connection.connection.appliesNetName.label;
	        }
	        return undefined;
	    }
	    transformConnectivityName(netObject) {
	        if (netObject.label?.labelType !== 'hier_label') {
	            return undefined;
	        }
	        const escapedName = KicadNetNamingStrategy$1.escapeNetName(netObject.label.originalLabel);
	        return this.globalDriverNames.has(escapedName) ? escapedName : this.sheetPath + escapedName;
	    }
	    canLabelTypeNameNets() {
	        return true;
	    }
	    static getLabelPriority(type) {
	        switch (type) {
	            case 'global':
	                return 6;
	            case 'power_pin':
	                return 5;
	            case 'local_label':
	                return 4;
	            case 'hier_label':
	                return 3;
	            case 'sheet_pin':
	                return 2;
	            case 'pin':
	            case 'pin_hint':
	                return 1;
	            case 'bus':
	                return -1;
	            default:
	                return 0;
	        }
	    }
	    resolveNetName(net) {
	        const createCandidate = (label, type) => {
	            const pinHint = this.pinHintNames.get(label);
	            return {
	                label: pinHint?.connected ?? label,
	                ...(pinHint ?
	                    {
	                        netName: label,
	                        unconnectedLabel: pinHint.unconnected,
	                        unconnectedNetName: this.getUnconnectedPinHintName(label),
	                    }
	                    : {}),
	                priority: KicadNetNamingStrategy$1.getLabelPriority(type),
	                type,
	            };
	        };
	        const labels = net.labels
	            .filter((l) => l.canNameNet)
	            .map((l) => createCandidate(l.formattedLabel ?? l.originalLabel, l.labelType))
	            .concat(net.connections
	            .map((c) => c.connection.appliesNetName ?
	            createCandidate(c.connection.appliesNetName.label, c.connection.appliesNetName.labelType)
	            : null)
	            .filter((c) => c !== null))
	            .sort((a, b) => KicadNetNamingStrategy$1.compareDriverCandidates(a, b));
	        let namingLabel = labels?.filter((l) => l.type !== 'pin' && l.priority > KicadNetNamingStrategy$1.getLabelPriority('pin'))[0];
	        if (namingLabel) {
	            return namingLabel.label;
	        }
	        namingLabel = labels[0];
	        if (namingLabel) {
	            const isSoleDriver = net.labels.length === 0 && net.connections.length === 1;
	            if (isSoleDriver || this.touchesNoConnectMarker(net)) {
	                const unconnectedName = labels
	                    .map((l) => ({
	                    ...l,
	                    label: l.unconnectedLabel ?? this.unconnectedPinNetNames.get(l.label) ?? l.label,
	                    netName: l.unconnectedNetName ?? this.unconnectedPinNetNames.get(l.label) ?? l.netName,
	                }))
	                    .sort((a, b) => KicadNetNamingStrategy$1.compareDriverCandidates(a, b))[0];
	                return unconnectedName.netName ?? unconnectedName.label;
	            }
	            return namingLabel.netName ?? namingLabel.label;
	        }
	        let implicitNetName = undefined;
	        for (const obj of net.netObjects) {
	            if (this.implicitNetNames.has(obj)) {
	                implicitNetName = this.implicitNetNames.get(obj);
	                break;
	            }
	        }
	        if (!implicitNetName) {
	            implicitNetName = `Net-${this.netNo++}`;
	        }
	        for (const obj of net.netObjects) {
	            this.implicitNetNames.set(obj, implicitNetName);
	        }
	        return implicitNetName;
	    }
	    resolvePhysicalNetNames(hierarchy) {
	        return new NetNamingRuleChain_1.NetNamingRuleChain().add(new IndividualGroupingRule_1.IndividualGroupingRule()).execute(hierarchy);
	    }
	    createVirtualRootHierarchy(roots) {
	        if (roots.length === 0) {
	            throw new Error('createVirtualRootHierarchy requires at least one root');
	        }
	        const virtualInstance = roots[0].instance;
	        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this, virtualInstance, { autoAssignNets: false, pointRoundingPrecision: 0.1 });
	        return {
	            pathUniqueId: '__virtual_root__',
	            hierarchyDepth: -1,
	            instance: virtualInstance,
	            combobulator: emptyCombobulator,
	            children: roots,
	        };
	    }
	    addNoConnectMarker(position, tolerance) {
	        this.noConnectMarkers.push({ position, tolerance });
	    }
	    nameSymbolPin(pinInfo) {
	        const name = this.getPinNetName(pinInfo);
	        const unconnectedName = this.getPinNetName(pinInfo, true);
	        if (name && unconnectedName) {
	            this.unconnectedPinNetNames.set(name, unconnectedName);
	        }
	        return name;
	    }
	    nameSymbolPinHint(pinInfo, duplicateIndex) {
	        const connected = this.getPinNetName(pinInfo);
	        const unconnected = this.getPinNetName(pinInfo, true);
	        if (!connected || !unconnected) {
	            return connected;
	        }
	        const key = `${connected}__kicad_pin_hint_${duplicateIndex}`;
	        const unconnectedKey = `${unconnected}__kicad_pin_hint_${duplicateIndex}`;
	        this.pinHintNames.set(key, { connected, unconnected, declarationIndex: duplicateIndex, isUnconnected: false });
	        this.pinHintNames.set(unconnectedKey, {
	            connected,
	            unconnected,
	            declarationIndex: duplicateIndex,
	            isUnconnected: true,
	        });
	        return key;
	    }
	    resolvePinHintNetNames(nets) {
	        const winnersByName = new Map();
	        for (const net of nets) {
	            const name = String(net.name.strCast());
	            const pinHint = this.pinHintNames.get(name);
	            if (!pinHint) {
	                continue;
	            }
	            const winners = winnersByName.get(pinHint.connected) ?? [];
	            winners.push({ name, pinHint });
	            winnersByName.set(pinHint.connected, winners);
	        }
	        const resolvedNames = new Map();
	        for (const winners of winnersByName.values()) {
	            winners.sort((left, right) => left.pinHint.declarationIndex - right.pinHint.declarationIndex);
	            for (const [index, winner] of winners.entries()) {
	                const suffix = index === winners.length - 1 ? '' : `_${index + 1}`;
	                const baseName = winner.pinHint.isUnconnected ? winner.pinHint.unconnected : winner.pinHint.connected;
	                resolvedNames.set(winner.name, `${baseName}${suffix}`);
	            }
	        }
	        return resolvedNames;
	    }
	    getUnconnectedPinHintName(name) {
	        const pinHint = this.pinHintNames.get(name);
	        if (!pinHint) {
	            return name;
	        }
	        return `${pinHint.unconnected}__kicad_pin_hint_${pinHint.declarationIndex}`;
	    }
	    getPinNetName(pinInfo, forceNoConnect = false) {
	        const isUnconnected = forceNoConnect || pinInfo.electricalType === 'no_connect';
	        const prefix = isUnconnected ? 'unconnected-(' : 'Net-(';
	        const refDes = pinInfo.refDes;
	        if (refDes.startsWith('#')) {
	            return undefined;
	        }
	        if (!pinInfo.isAnnotated && pinInfo.symbolUuid) {
	            return `${prefix}${pinInfo.symbolUuid}-Pad${pinInfo.pinNumber})`;
	        }
	        const pinShownName = KicadNetNamingStrategy$1.escapeNetName(pinInfo.pinName);
	        const pinShownNumber = KicadNetNamingStrategy$1.escapeNetName(pinInfo.pinNumber);
	        const hasPinName = pinShownName !== '~' && pinShownName.length > 0 && pinShownName !== pinShownNumber;
	        if (hasPinName) {
	            const unitRefDes = pinInfo.unitRefDes ?? refDes;
	            if (isUnconnected || pinInfo.hasMultiplePinsWithSameName) {
	                return `${prefix}${unitRefDes}-${pinShownName}-Pad${pinShownNumber})`;
	            }
	            return `${prefix}${unitRefDes}-${pinShownName})`;
	        }
	        return `${prefix}${refDes}-Pad${pinShownNumber})`;
	    }
	    static escapeNetName(text) {
	        return text.replace(/\//g, '{slash}').replace(/[\r\n]/g, '');
	    }
	    touchesNoConnectMarker(net) {
	        if (this.noConnectMarkers.length === 0) {
	            return false;
	        }
	        const points = net.connections.map((c) => c.position).concat(net.wires.flatMap((w) => [w.from, w.to]));
	        return this.noConnectMarkers.some(({ position, tolerance }) => points.some((p) => Math.abs(p[0] - position[0]) <= tolerance && Math.abs(p[1] - position[1]) <= tolerance));
	    }
	    static compareDriverCandidates(a, b) {
	        if (b.priority !== a.priority) {
	            return b.priority - a.priority;
	        }
	        const aIsLowQuality = (a.type === 'pin' || a.type === 'pin_hint') && a.label.includes('-Pad');
	        const bIsLowQuality = (b.type === 'pin' || b.type === 'pin_hint') && b.label.includes('-Pad');
	        if (aIsLowQuality !== bIsLowQuality) {
	            return aIsLowQuality ? 1 : -1;
	        }
	        return KicadNetNamingStrategy$1.compareOrdinal(a.label, b.label);
	    }
	    static compareOrdinal(a, b) {
	        if (a < b) {
	            return -1;
	        }
	        return a > b ? 1 : 0;
	    }
	    static hasAutoGeneratedNetname(netName) {
	        return netName.startsWith('Net-(') || netName.startsWith('unconnected-(');
	    }
	}
	KicadNetNamingStrategy.KicadNetNamingStrategy = KicadNetNamingStrategy$1;
	
	return KicadNetNamingStrategy;
}

var KicadBusTopology = {};

var hasRequiredKicadBusTopology;

function requireKicadBusTopology () {
	if (hasRequiredKicadBusTopology) return KicadBusTopology;
	hasRequiredKicadBusTopology = 1;
	Object.defineProperty(KicadBusTopology, "__esModule", { value: true });
	KicadBusTopology.KicadBusTopology = KicadBusTopology.KicadBusExpressionType = KicadBusTopology.KicadBusDriverType = void 0;
	var KicadBusDriverType;
	(function (KicadBusDriverType) {
	    KicadBusDriverType["LocalLabel"] = "local_bus_label";
	    KicadBusDriverType["HierarchicalLabel"] = "hierarchical_bus_label";
	    KicadBusDriverType["SheetPin"] = "bus_sheet_pin";
	})(KicadBusDriverType || (KicadBusTopology.KicadBusDriverType = KicadBusDriverType = {}));
	var KicadBusExpressionType;
	(function (KicadBusExpressionType) {
	    KicadBusExpressionType["Group"] = "group";
	    KicadBusExpressionType["Vector"] = "vector";
	})(KicadBusExpressionType || (KicadBusTopology.KicadBusExpressionType = KicadBusExpressionType = {}));
	class KicadBusTopology$1 {
	    constructor() {
	        this.tracks = [];
	        this.junctions = [];
	        this.drivers = [];
	    }
	    static isBusLabel(text) {
	        return KicadBusTopology$1.parseBusLabel(text) !== undefined;
	    }
	    static parseBusLabel(text) {
	        const vector = text.match(/^([^\s[\]]*)\[(-?\d+)\.\.(-?\d+)\]$/);
	        if (vector) {
	            const first = BigInt(vector[2]);
	            const last = BigInt(vector[3]);
	            const step = first <= last ? 1n : -1n;
	            const members = [];
	            for (let member = first;; member += step) {
	                members.push(String(member));
	                if (member === last) {
	                    break;
	                }
	            }
	            return { type: KicadBusExpressionType.Vector, prefix: vector[1], members };
	        }
	        const group = this.findOuterGroup(text);
	        if (!group || /[\s[\]]/.test(group.prefix) || /[~^_]$/.test(group.prefix)) {
	            return undefined;
	        }
	        return { type: KicadBusExpressionType.Group, prefix: group.prefix, members: this.splitMembers(group.contents) };
	    }
	    addTrack(track) {
	        this.tracks.push(track);
	    }
	    addJunction(position) {
	        this.junctions.push(position);
	    }
	    addDriver(text, type, position, moduleRef, pinRef) {
	        this.drivers.push({ text, type, position, moduleRef, pinRef });
	    }
	    build() {
	        const parents = Array.from({ length: this.tracks.length + this.drivers.length }, (_, index) => index);
	        for (let first = 0; first < this.tracks.length; first++) {
	            for (let second = first + 1; second < this.tracks.length; second++) {
	                if (this.shareEndpoint(this.tracks[first], this.tracks[second])) {
	                    this.unionNodes(parents, first, second);
	                }
	            }
	        }
	        for (const junction of this.junctions) {
	            const touching = this.tracks
	                .map((track, index) => ({ track, index }))
	                .filter(({ track }) => this.isOnTrack(junction, track));
	            for (const { index } of touching.slice(1)) {
	                this.unionNodes(parents, touching[0].index, index);
	            }
	        }
	        for (let driverIndex = 0; driverIndex < this.drivers.length; driverIndex++) {
	            const nodeIndex = this.tracks.length + driverIndex;
	            for (let trackIndex = 0; trackIndex < this.tracks.length; trackIndex++) {
	                if (this.isOnTrack(this.drivers[driverIndex].position, this.tracks[trackIndex])) {
	                    this.unionNodes(parents, nodeIndex, trackIndex);
	                }
	            }
	            for (let previous = 0; previous < driverIndex; previous++) {
	                if (this.samePoint(this.drivers[driverIndex].position, this.drivers[previous].position)) {
	                    this.unionNodes(parents, nodeIndex, this.tracks.length + previous);
	                }
	            }
	        }
	        const componentIds = new Map();
	        const components = new Map();
	        for (let index = 0; index < this.drivers.length; index++) {
	            const driver = this.drivers[index];
	            const root = this.findNode(parents, this.tracks.length + index);
	            const componentId = componentIds.get(root) ?? `bus-${componentIds.size}`;
	            componentIds.set(root, componentId);
	            const component = components.get(componentId) ?? { id: componentId, drivers: [] };
	            component.drivers.push({
	                text: driver.text,
	                type: driver.type,
	                componentId,
	                moduleRef: driver.moduleRef,
	                pinRef: driver.pinRef,
	            });
	            components.set(componentId, component);
	        }
	        return [...components.values()];
	    }
	    findNode(parents, index) {
	        const parent = parents[index];
	        if (parent === index) {
	            return index;
	        }
	        const root = this.findNode(parents, parent);
	        parents[index] = root;
	        return root;
	    }
	    unionNodes(parents, first, second) {
	        const firstRoot = this.findNode(parents, first);
	        const secondRoot = this.findNode(parents, second);
	        if (firstRoot !== secondRoot) {
	            parents[secondRoot] = firstRoot;
	        }
	    }
	    static splitMembers(contents) {
	        const members = [];
	        let depth = 0;
	        let start = 0;
	        for (let index = 0; index <= contents.length; index++) {
	            const character = contents[index];
	            if (character === '{') {
	                depth++;
	            }
	            else if (character === '}') {
	                depth--;
	            }
	            if ((index === contents.length || /\s/.test(character)) && depth === 0) {
	                const member = contents.slice(start, index).trim();
	                if (member.length > 0) {
	                    members.push(member);
	                }
	                start = index + 1;
	            }
	        }
	        return members;
	    }
	    static findOuterGroup(text) {
	        for (let open = 0; open < text.length; open++) {
	            if (text[open] !== '{') {
	                continue;
	            }
	            let depth = 0;
	            for (let close = open; close < text.length; close++) {
	                if (text[close] === '{') {
	                    depth++;
	                }
	                else if (text[close] === '}') {
	                    depth--;
	                    if (depth === 0 && close === text.length - 1) {
	                        return { prefix: text.slice(0, open), contents: text.slice(open + 1, close) };
	                    }
	                }
	            }
	        }
	        return undefined;
	    }
	    shareEndpoint(first, second) {
	        return (this.samePoint(first.start, second.start) ||
	            this.samePoint(first.start, second.end) ||
	            this.samePoint(first.end, second.start) ||
	            this.samePoint(first.end, second.end));
	    }
	    isOnTrack(point, track) {
	        const [x, y] = point;
	        const [startX, startY] = track.start;
	        const [endX, endY] = track.end;
	        const cross = (x - startX) * (endY - startY) - (y - startY) * (endX - startX);
	        if (Math.abs(cross) > 0.000001) {
	            return false;
	        }
	        return (x >= Math.min(startX, endX) - 0.000001 &&
	            x <= Math.max(startX, endX) + 0.000001 &&
	            y >= Math.min(startY, endY) - 0.000001 &&
	            y <= Math.max(startY, endY) + 0.000001);
	    }
	    samePoint(first, second) {
	        return Math.abs(first[0] - second[0]) < 0.000001 && Math.abs(first[1] - second[1]) < 0.000001;
	    }
	}
	KicadBusTopology.KicadBusTopology = KicadBusTopology$1;
	
	return KicadBusTopology;
}

var hasRequiredKicadHierarchyResolver;

function requireKicadHierarchyResolver () {
	if (hasRequiredKicadHierarchyResolver) return KicadHierarchyResolver;
	hasRequiredKicadHierarchyResolver = 1;
	Object.defineProperty(KicadHierarchyResolver, "__esModule", { value: true });
	KicadHierarchyResolver.recordKicadBusAliases = recordKicadBusAliases;
	KicadHierarchyResolver.recordKicadBusSheet = recordKicadBusSheet;
	KicadHierarchyResolver.recordKicadNetDrivers = recordKicadNetDrivers;
	KicadHierarchyResolver.resolveKicadHierarchyNets = resolveKicadHierarchyNets;
	const sigma_engine_shared_1 = requireDist$1();
	const RefableNet_1 = requireRefableNet();
	const KicadNetNamingStrategy_1 = requireKicadNetNamingStrategy();
	const KicadBusTopology_1 = requireKicadBusTopology();
	var KicadHierarchyProcessingResultType;
	(function (KicadHierarchyProcessingResultType) {
	    KicadHierarchyProcessingResultType["MissingChildDesign"] = "kicad-hierarchy-missing-child-design";
	    KicadHierarchyProcessingResultType["MissingChildPort"] = "kicad-hierarchy-missing-child-port";
	    KicadHierarchyProcessingResultType["MissingParentNet"] = "kicad-hierarchy-missing-parent-net";
	    KicadHierarchyProcessingResultType["MissingChildNet"] = "kicad-hierarchy-missing-child-net";
	    KicadHierarchyProcessingResultType["MissingSheetSymbol"] = "kicad-hierarchy-missing-sheet-symbol";
	})(KicadHierarchyProcessingResultType || (KicadHierarchyProcessingResultType = {}));
	const netDrivers = new WeakMap();
	const projectBusAliases = new WeakMap();
	const designBusComponents = new WeakMap();
	function getSheetDepth(sheetPath) {
	    return sheetPath.split('/').filter(Boolean).length;
	}
	function recordKicadBusAliases(project, aliases, sheetPath) {
	    const projectAliases = projectBusAliases.get(project) ?? new Map();
	    projectAliases.set(sheetPath, aliases);
	    projectBusAliases.set(project, projectAliases);
	}
	function recordKicadBusSheet(design, components) {
	    designBusComponents.set(design, components);
	}
	var KicadBusDriverPriority;
	(function (KicadBusDriverPriority) {
	    KicadBusDriverPriority[KicadBusDriverPriority["SheetPin"] = 1] = "SheetPin";
	    KicadBusDriverPriority[KicadBusDriverPriority["HierarchicalLabel"] = 2] = "HierarchicalLabel";
	    KicadBusDriverPriority[KicadBusDriverPriority["LocalLabel"] = 3] = "LocalLabel";
	})(KicadBusDriverPriority || (KicadBusDriverPriority = {}));
	const KICAD_BUS_MEMBER_PRIORITY = 4.5;
	class KicadBusHierarchyResolver {
	    constructor(project, netParents, netIndexes) {
	        this.project = project;
	        this.netParents = netParents;
	        this.netIndexes = netIndexes;
	        this.slots = new Map();
	        this.slotParents = new Map();
	        this.sheetPaths = new Map();
	        this.localLabels = new Map();
	        this.aliasesBySheetPath = projectBusAliases.get(project) ?? new Map();
	        this.scalarExitWinners = this.findScalarExitWinners();
	        this.indexSheetLabels();
	    }
	    resolve() {
	        this.buildSlots();
	        this.joinHierarchySlots();
	        for (const group of this.groupSlots().values()) {
	            const nets = new Set(group.flatMap((slot) => [...slot.nets]));
	            const winner = group
	                .flatMap((slot) => slot.drivers)
	                .sort((first, second) => this.compareDrivers(first, second))[0];
	            if (!winner || nets.size === 0) {
	                continue;
	            }
	            const [firstNet, ...otherNets] = [...nets];
	            for (const net of otherNets) {
	                unionNets(this.netParents, firstNet, net);
	            }
	            for (const net of nets) {
	                const candidates = netDrivers.get(net) ?? [];
	                candidates.push({
	                    label: winner.label,
	                    priority: KICAD_BUS_MEMBER_PRIORITY,
	                    type: 'bus',
	                    hierarchyDepth: winner.hierarchyDepth,
	                    sheetPath: '/',
	                });
	                netDrivers.set(net, candidates);
	            }
	        }
	    }
	    buildSlots() {
	        for (const [design] of this.netIndexes) {
	            for (const component of designBusComponents.get(design) ?? []) {
	                for (const driver of component.drivers) {
	                    for (const member of this.expandBus(design, driver.text)) {
	                        const slot = this.getOrCreateSlot(design, component.id, member.key);
	                        slot.drivers.push(this.createDriver(design, driver, member));
	                        for (const net of this.localLabels.get(design)?.get(member.escapedName) ?? []) {
	                            if (!this.isScalarExitOwnedByOtherLabel(design, net, member)) {
	                                slot.nets.add(net);
	                            }
	                        }
	                    }
	                }
	            }
	        }
	    }
	    joinHierarchySlots() {
	        for (const parentDesign of this.netIndexes.keys()) {
	            for (const drawing of Object.values(parentDesign.drawings).flat()) {
	                if (drawing.type !== 'schref' || !drawing.designId) {
	                    continue;
	                }
	                const childDesign = this.project.designs[drawing.designId];
	                if (!isSchematicDesign(childDesign)) {
	                    continue;
	                }
	                const parentDrivers = (designBusComponents.get(parentDesign) ?? [])
	                    .flatMap((component) => component.drivers)
	                    .filter((driver) => driver.type === KicadBusTopology_1.KicadBusDriverType.SheetPin && driver.moduleRef === drawing.drawModule.ref);
	                const childDrivers = (designBusComponents.get(childDesign) ?? [])
	                    .flatMap((component) => component.drivers)
	                    .filter((driver) => driver.type === KicadBusTopology_1.KicadBusDriverType.HierarchicalLabel);
	                for (const parentDriver of parentDrivers) {
	                    for (const childDriver of childDrivers) {
	                        if (parentDriver.text !== childDriver.text) {
	                            continue;
	                        }
	                        for (const member of this.expandBus(parentDesign, parentDriver.text)) {
	                            const parentSlot = this.slots.get(this.slotId(parentDesign, parentDriver.componentId, member.key));
	                            const childSlot = this.slots.get(this.slotId(childDesign, childDriver.componentId, member.key));
	                            if (parentSlot && childSlot) {
	                                this.unionSlots(parentSlot, childSlot);
	                            }
	                        }
	                    }
	                }
	            }
	        }
	    }
	    expandBus(design, text) {
	        const expression = KicadBusTopology_1.KicadBusTopology.parseBusLabel(text);
	        if (!expression) {
	            return [];
	        }
	        const members = [];
	        const sourceMembers = expression.type === KicadBusTopology_1.KicadBusExpressionType.Group ?
	            (this.getAliasMembers(design, expression.members.join(' ')) ?? expression.members)
	            : expression.members;
	        for (const sourceMember of sourceMembers) {
	            const nested = KicadBusTopology_1.KicadBusTopology.parseBusLabel(sourceMember);
	            const memberNames = nested ? nested.members : [sourceMember];
	            for (const member of memberNames) {
	                const nestedName = nested?.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? `${nested.prefix}${member}` : member;
	                const name = expression.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? `${expression.prefix}${member}`
	                    : expression.prefix.length > 0 ? `${expression.prefix}.${nestedName}`
	                        : nestedName;
	                const key = expression.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? member
	                    : nested?.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? nestedName
	                        : member;
	                members.push({ key, name, escapedName: KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(name) });
	            }
	        }
	        return members;
	    }
	    getAliasMembers(design, aliasName) {
	        const candidates = [...this.aliasesBySheetPath].filter(([, aliases]) => aliases.has(aliasName));
	        const own = this.getDesignSheetPath(design) === '/' ?
	            undefined
	            : candidates.find(([path]) => path === this.getDesignSheetPath(design));
	        if (own) {
	            return own[1].get(aliasName);
	        }
	        candidates.sort(([first], [second]) => {
	            const depthDifference = getSheetDepth(second) - getSheetDepth(first);
	            return (depthDifference !== 0 ? depthDifference
	                : first < second ? -1
	                    : first > second ? 1
	                        : 0);
	        });
	        return candidates[0]?.[1].get(aliasName);
	    }
	    isScalarExitOwnedByOtherLabel(design, net, member) {
	        const winner = this.scalarExitWinners.get(net);
	        if (winner?.type === 'local_label') {
	            return winner.label !== this.getDesignSheetPath(design) + member.escapedName;
	        }
	        if (winner?.type === 'global' || winner?.type === 'power_pin') {
	            return !(netDrivers.get(net) ?? []).some((candidate) => candidate.type === 'global' || candidate.type === 'power_pin');
	        }
	        return false;
	    }
	    getOrCreateSlot(design, componentId, memberKey) {
	        const id = this.slotId(design, componentId, memberKey);
	        let slot = this.slots.get(id);
	        if (!slot) {
	            slot = { id, design, componentId, memberKey, nets: new Set(), drivers: [] };
	            this.slots.set(id, slot);
	            this.slotParents.set(slot, slot);
	        }
	        return slot;
	    }
	    slotId(design, componentId, memberKey) {
	        return `${design.designId}\0${componentId}\0${memberKey}`;
	    }
	    createDriver(design, driver, member) {
	        const sheetPath = this.getDesignSheetPath(design);
	        return {
	            label: sheetPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(member.name),
	            priority: this.busDriverPriority(driver.type),
	            hierarchyDepth: sheetPath.split('/').filter(Boolean).length,
	        };
	    }
	    busDriverPriority(type) {
	        switch (type) {
	            case KicadBusTopology_1.KicadBusDriverType.LocalLabel:
	                return KicadBusDriverPriority.LocalLabel;
	            case KicadBusTopology_1.KicadBusDriverType.HierarchicalLabel:
	                return KicadBusDriverPriority.HierarchicalLabel;
	            case KicadBusTopology_1.KicadBusDriverType.SheetPin:
	                return KicadBusDriverPriority.SheetPin;
	        }
	    }
	    getDesignSheetPath(design) {
	        return this.sheetPaths.get(design) ?? '/';
	    }
	    findScalarExitWinners() {
	        const exits = new Map();
	        for (const group of groupNets(this.netParents).values()) {
	            const winner = getGroupWinners(new Map([[group[0], group]])).get(group[0]);
	            if (!winner) {
	                continue;
	            }
	            for (const net of group) {
	                const hasChildHierarchicalLabel = (netDrivers.get(net) ?? []).some((candidate) => candidate.hierarchyDepth > 0 && candidate.type === 'hier_label');
	                if (hasChildHierarchicalLabel) {
	                    exits.set(net, winner);
	                }
	            }
	        }
	        return exits;
	    }
	    indexSheetLabels() {
	        for (const [design, netIndex] of this.netIndexes) {
	            const labels = new Map();
	            let sheetPath = '/';
	            for (const net of netIndex.values()) {
	                const path = netDrivers.get(net)?.[0]?.sheetPath;
	                if (path !== undefined) {
	                    sheetPath = path;
	                }
	                for (const label of net.netLabels ?? []) {
	                    if (label.type !== 'local_label') {
	                        continue;
	                    }
	                    const authored = label.label.startsWith(sheetPath) ? label.label.slice(sheetPath.length) : label.label;
	                    const nets = labels.get(authored) ?? new Set();
	                    nets.add(net);
	                    labels.set(authored, nets);
	                }
	            }
	            this.sheetPaths.set(design, sheetPath);
	            this.localLabels.set(design, labels);
	        }
	    }
	    findSlot(slot) {
	        const parent = this.slotParents.get(slot);
	        if (!parent || parent === slot) {
	            return slot;
	        }
	        const root = this.findSlot(parent);
	        this.slotParents.set(slot, root);
	        return root;
	    }
	    unionSlots(first, second) {
	        const firstRoot = this.findSlot(first);
	        const secondRoot = this.findSlot(second);
	        if (firstRoot !== secondRoot) {
	            this.slotParents.set(secondRoot, firstRoot);
	        }
	    }
	    groupSlots() {
	        const groups = new Map();
	        for (const slot of this.slots.values()) {
	            const root = this.findSlot(slot);
	            const group = groups.get(root) ?? [];
	            group.push(slot);
	            groups.set(root, group);
	        }
	        return groups;
	    }
	    compareDrivers(first, second) {
	        if (first.priority !== second.priority) {
	            return second.priority - first.priority;
	        }
	        if (first.hierarchyDepth !== second.hierarchyDepth) {
	            return first.hierarchyDepth - second.hierarchyDepth;
	        }
	        return (first.label < second.label ? -1
	            : first.label > second.label ? 1
	                : 0);
	    }
	}
	function recordKicadNetDrivers(netInfo, net, netPath) {
	    const hierarchyDepth = netPath.split('/').filter(Boolean).length;
	    const candidates = [];
	    for (const label of net.labels) {
	        if (!label.canNameNet) {
	            continue;
	        }
	        const formattedLabel = label.labelType === 'hier_label' ?
	            netPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(label.originalLabel)
	            : (label.formattedLabel ?? label.originalLabel);
	        candidates.push({
	            label: formattedLabel,
	            priority: KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority(label.labelType),
	            type: label.labelType,
	            hierarchyDepth,
	            sheetPath: netPath,
	        });
	    }
	    for (const connection of net.connections) {
	        const driver = connection.connection.appliesNetName;
	        if (!driver) {
	            continue;
	        }
	        candidates.push({
	            label: driver.label,
	            priority: KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority(driver.labelType),
	            type: driver.labelType,
	            hierarchyDepth,
	            sheetPath: netPath,
	        });
	    }
	    const resolvedName = String(netInfo.name);
	    for (const candidate of candidates) {
	        if (candidate.type === 'pin_hint') {
	            candidate.label = resolvedName;
	        }
	    }
	    if (resolvedName.startsWith('unconnected-')) {
	        for (const candidate of candidates) {
	            if (candidate.type === 'pin') {
	                candidate.label = resolvedName;
	            }
	        }
	    }
	    netDrivers.set(netInfo, candidates);
	}
	function resolveKicadHierarchyNets(project) {
	    const parent = new Map();
	    const netIndexes = new Map();
	    const netOwners = new WeakMap();
	    const unmatchedSheetPins = [];
	    const matchedSheetPins = new Map();
	    for (const design of Object.values(project.designs)) {
	        if (!isSchematicDesign(design)) {
	            continue;
	        }
	        const netIndex = indexDesignNets(design);
	        netIndexes.set(design, netIndex);
	        for (const net of netIndex.values()) {
	            netOwners.set(net, design);
	            if (netDrivers.has(net)) {
	                parent.set(net, net);
	            }
	        }
	    }
	    for (const parentDesign of Object.values(project.designs)) {
	        if (!isSchematicDesign(parentDesign)) {
	            continue;
	        }
	        const parentNetIndex = netIndexes.get(parentDesign);
	        if (!parentNetIndex) {
	            continue;
	        }
	        for (const drawing of Object.values(parentDesign.drawings).flat()) {
	            if (drawing.type !== 'schref') {
	                continue;
	            }
	            const module = drawing.drawModule;
	            const component = project.components[module.componentRef];
	            const symbol = component?.symbols.find((candidate) => candidate.ref === module.footprintRef);
	            if (!symbol) {
	                recordHierarchySheetWarning(project, KicadHierarchyProcessingResultType.MissingSheetSymbol, drawing.name, 'sheet symbol component or symbol definition');
	                continue;
	            }
	            const childDesign = drawing.designId ? project.designs[drawing.designId] : undefined;
	            if (!isSchematicDesign(childDesign)) {
	                recordHierarchySheetWarning(project, KicadHierarchyProcessingResultType.MissingChildDesign, drawing.name, 'child schematic design');
	                for (const pin of symbol.pins) {
	                    recordUnmatchedSheetPin(unmatchedSheetPins, module.pinNets[pin.ref], parentNetIndex, String(pin.name), parentDesign);
	                }
	                continue;
	            }
	            for (const pin of symbol.pins) {
	                const pinName = String(pin.name);
	                if (KicadBusTopology_1.KicadBusTopology.isBusLabel(pinName)) {
	                    continue;
	                }
	                const parentNetName = module.pinNets[pin.ref];
	                const parentNet = parentNetName ? parentNetIndex.get(String(parentNetName)) : undefined;
	                if (!parentNet) {
	                    recordHierarchyWarning(project, KicadHierarchyProcessingResultType.MissingParentNet, drawing.name, pinName, 'parent sheet-pin net');
	                }
	                const childPort = childDesign.ports[pinName];
	                if (!childPort) {
	                    recordHierarchyWarning(project, KicadHierarchyProcessingResultType.MissingChildPort, drawing.name, pinName, 'child hierarchical port');
	                    recordUnmatchedSheetPin(unmatchedSheetPins, parentNetName, parentNetIndex, pinName, parentDesign);
	                    continue;
	                }
	                if (!parentNet) {
	                    continue;
	                }
	                const matchedPins = matchedSheetPins.get(parentDesign) ?? [];
	                matchedPins.push({
	                    net: parentNet,
	                    name: getNetSheetPath(parentNet) + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(pinName),
	                    isSoleSheetPinDriver: (netDrivers.get(parentNet) ?? []).filter((candidate) => candidate.type === 'sheet_pin').length === 1,
	                });
	                matchedSheetPins.set(parentDesign, matchedPins);
	                const childNetIndex = netIndexes.get(childDesign);
	                for (const portNet of childPort.nets) {
	                    const childNetName = String(portNet.name);
	                    const childNet = childNetIndex?.get(childNetName);
	                    if (!childNet) {
	                        recordHierarchyWarning(project, KicadHierarchyProcessingResultType.MissingChildNet, drawing.name, pinName, `child port net "${childNetName}"`);
	                        continue;
	                    }
	                    unionNets(parent, parentNet, childNet);
	                }
	            }
	        }
	    }
	    new KicadBusHierarchyResolver(project, parent, netIndexes).resolve();
	    const groups = groupNets(parent);
	    const initialGroupWinners = getGroupWinners(groups);
	    removeSheetPinDrivers(netIndexes);
	    recordUnmatchedSheetPinDrivers(unmatchedSheetPins, matchedSheetPins, netIndexes, initialGroupWinners);
	    const groupWinners = getGroupWinners(groups);
	    const renamedByDesign = new Map();
	    for (const group of groups.values()) {
	        const winner = groupWinners.get(group[0]);
	        if (!winner) {
	            continue;
	        }
	        for (const net of group) {
	            const owner = netOwners.get(net);
	            if (!owner) {
	                continue;
	            }
	            const renamed = renamedByDesign.get(owner) ?? new Map();
	            renamed.set(String(net.name), winner.label);
	            renamedByDesign.set(owner, renamed);
	            if ((0, sigma_engine_shared_1.isNetRef)(net.name)) {
	                net.name.setName(winner.label);
	            }
	        }
	    }
	    for (const [design, renamed] of renamedByDesign) {
	        rekeyDesignNetReferences(design, renamed);
	    }
	}
	function getNetSheetPath(net) {
	    return netDrivers.get(net)?.[0]?.sheetPath ?? '/';
	}
	function recordUnmatchedSheetPinDrivers(unmatchedPins, matchedPinsByOwner, netIndexes, groupWinners) {
	    const candidatesByOwner = new Map();
	    for (const unmatchedPin of unmatchedPins) {
	        const winner = groupWinners.get(unmatchedPin.net);
	        if (winner && winner.priority > KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority('sheet_pin')) {
	            continue;
	        }
	        if (!netDrivers.get(unmatchedPin.net)?.length) {
	            continue;
	        }
	        const baseName = unmatchedPin.sheetPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(unmatchedPin.pinName);
	        const candidatesByName = candidatesByOwner.get(unmatchedPin.owner) ??
	            new Map();
	        const candidatesByNet = candidatesByName.get(baseName) ?? new Map();
	        candidatesByNet.set(unmatchedPin.net, unmatchedPin);
	        candidatesByName.set(baseName, candidatesByNet);
	        candidatesByOwner.set(unmatchedPin.owner, candidatesByName);
	    }
	    for (const [owner, candidatesByName] of candidatesByOwner) {
	        const netIndex = netIndexes.get(owner);
	        if (!netIndex) {
	            continue;
	        }
	        const nameIndex = getSheetPinNameIndex(netIndex, candidatesByName, matchedPinsByOwner.get(owner) ?? [], groupWinners);
	        for (const [baseName, candidatesByNet] of candidatesByName) {
	            if (candidatesByNet.size !== 1) {
	                continue;
	            }
	            const [unmatchedPin] = candidatesByNet.values();
	            if (!unmatchedPin) {
	                continue;
	            }
	            const labelOwners = nameIndex.labelOwners.get(baseName);
	            const matchedSheetPinOwners = nameIndex.matchedSheetPinOwners.get(baseName);
	            const hasOtherEligibleOwner = (labelOwners ? [...labelOwners].some((net) => net !== unmatchedPin.net) : false) ||
	                (matchedSheetPinOwners ? [...matchedSheetPinOwners].some((net) => net !== unmatchedPin.net) : false);
	            if (hasOtherEligibleOwner) {
	                const suffixedName = getFirstAvailableSheetPinSuffix(baseName, nameIndex.reservedNames);
	                recordUnmatchedSheetPinDriver(unmatchedPin, suffixedName);
	                nameIndex.reservedNames.add(suffixedName);
	                continue;
	            }
	            const groupWinnerOwners = nameIndex.groupWinnerOwners.get(baseName);
	            if (groupWinnerOwners && [...groupWinnerOwners].some((net) => net !== unmatchedPin.net)) {
	                continue;
	            }
	            recordUnmatchedSheetPinDriver(unmatchedPin, baseName);
	            nameIndex.reservedNames.add(baseName);
	        }
	    }
	}
	function recordUnmatchedSheetPin(unmatchedPins, parentNetName, parentNetIndex, pinName, owner) {
	    const parentNet = parentNetName ? parentNetIndex.get(String(parentNetName)) : undefined;
	    if (!parentNet) {
	        return;
	    }
	    unmatchedPins.push({
	        net: parentNet,
	        pinName,
	        sheetPath: getNetSheetPath(parentNet),
	        owner,
	    });
	}
	function getSheetPinNameIndex(netIndex, candidatesByName, matchedPins, groupWinners) {
	    const reservedNames = new Set(candidatesByName.keys());
	    const labelOwners = new Map();
	    const matchedSheetPinOwners = new Map();
	    const groupWinnerOwners = new Map();
	    for (const net of netIndex.values()) {
	        reservedNames.add(String(net.name));
	        const winnerName = groupWinners.get(net)?.label;
	        if (winnerName) {
	            reservedNames.add(winnerName);
	            addSheetPinNameOwner(groupWinnerOwners, winnerName, net);
	        }
	        for (const candidate of netDrivers.get(net) ?? []) {
	            reservedNames.add(candidate.label);
	            if (!isLabelDriver(candidate)) {
	                continue;
	            }
	            addSheetPinNameOwner(labelOwners, candidate.label, net);
	            if (candidate.type === 'local_label') {
	                const pathQualifiedName = candidate.sheetPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(candidate.label);
	                reservedNames.add(pathQualifiedName);
	                addSheetPinNameOwner(labelOwners, pathQualifiedName, net);
	            }
	        }
	    }
	    for (const matchedPin of matchedPins) {
	        if (!matchedPin.isSoleSheetPinDriver) {
	            continue;
	        }
	        const hasStrongerParentDriver = netDrivers
	            .get(matchedPin.net)
	            ?.some((candidate) => candidate.priority > KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority('sheet_pin'));
	        if (hasStrongerParentDriver) {
	            continue;
	        }
	        reservedNames.add(matchedPin.name);
	        addSheetPinNameOwner(matchedSheetPinOwners, matchedPin.name, matchedPin.net);
	    }
	    return { reservedNames, labelOwners, matchedSheetPinOwners, groupWinnerOwners };
	}
	function addSheetPinNameOwner(owners, name, net) {
	    const namedOwners = owners.get(name) ?? new Set();
	    namedOwners.add(net);
	    owners.set(name, namedOwners);
	}
	function isLabelDriver(candidate) {
	    return candidate.type === 'local_label' || candidate.type === 'hier_label';
	}
	function getFirstAvailableSheetPinSuffix(baseName, reservedNames) {
	    let suffix = 1;
	    while (reservedNames.has(`${baseName}_${suffix}`)) {
	        suffix += 1;
	    }
	    return `${baseName}_${suffix}`;
	}
	function removeSheetPinDrivers(netIndexes) {
	    for (const netIndex of netIndexes.values()) {
	        for (const net of netIndex.values()) {
	            const candidates = netDrivers.get(net);
	            if (candidates) {
	                netDrivers.set(net, candidates.filter((candidate) => candidate.type !== 'sheet_pin'));
	            }
	        }
	    }
	}
	function recordUnmatchedSheetPinDriver(unmatchedPin, label) {
	    const candidates = netDrivers.get(unmatchedPin.net);
	    if (!candidates) {
	        return;
	    }
	    candidates.push({
	        label,
	        priority: KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority('sheet_pin'),
	        type: 'sheet_pin',
	        hierarchyDepth: unmatchedPin.sheetPath.split('/').filter(Boolean).length,
	        sheetPath: unmatchedPin.sheetPath,
	    });
	}
	function isSchematicDesign(design) {
	    return design?.modelType === 'schematic';
	}
	function indexDesignNets(design) {
	    const netIndex = new Map();
	    for (const net of (0, RefableNet_1.refableObjectValues)(design.nets)) {
	        netIndex.set(String(net.name), net);
	    }
	    return netIndex;
	}
	function findNet(parent, net) {
	    const current = parent.get(net);
	    if (!current || current === net) {
	        return net;
	    }
	    const root = findNet(parent, current);
	    parent.set(net, root);
	    return root;
	}
	function unionNets(parent, first, second) {
	    if (!parent.has(first)) {
	        parent.set(first, first);
	    }
	    if (!parent.has(second)) {
	        parent.set(second, second);
	    }
	    const firstRoot = findNet(parent, first);
	    const secondRoot = findNet(parent, second);
	    if (firstRoot !== secondRoot) {
	        parent.set(secondRoot, firstRoot);
	    }
	}
	function groupNets(parent) {
	    const groups = new Map();
	    for (const net of parent.keys()) {
	        const root = findNet(parent, net);
	        const group = groups.get(root) ?? [];
	        group.push(net);
	        groups.set(root, group);
	    }
	    return groups;
	}
	function getGroupWinners(groups) {
	    const winners = new Map();
	    for (const group of groups.values()) {
	        const winner = group.flatMap((net) => netDrivers.get(net) ?? []).sort(compareHierarchyDrivers)[0];
	        for (const net of group) {
	            winners.set(net, winner);
	        }
	    }
	    return winners;
	}
	function compareHierarchyDrivers(first, second) {
	    if (first.priority !== second.priority) {
	        return second.priority - first.priority;
	    }
	    if (first.hierarchyDepth !== second.hierarchyDepth) {
	        return first.hierarchyDepth - second.hierarchyDepth;
	    }
	    return KicadNetNamingStrategy_1.KicadNetNamingStrategy.compareDriverCandidates(first, second);
	}
	function recordHierarchyWarning(project, type, sheetName, pinName, missing) {
	    project.processingResults.innerResults.push({
	        type,
	        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        message: `KiCad hierarchy could not resolve ${missing} for sheet "${sheetName}" pin "${pinName}".`,
	    });
	}
	function recordHierarchySheetWarning(project, type, sheetName, missing) {
	    project.processingResults.innerResults.push({
	        type,
	        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        message: `KiCad hierarchy could not resolve ${missing} for sheet "${sheetName}".`,
	    });
	}
	function hasDrawingNetReferences(drawing) {
	    return 'net' in drawing || 'nets' in drawing;
	}
	function rekeyDesignNetReferences(design, renamed) {
	    for (const module of design.modules) {
	        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
	            module.pinNets[pinRef] = rekeyNetReference(netName, renamed);
	        }
	    }
	    for (const port of Object.values(design.ports)) {
	        for (const portNet of port.nets) {
	            portNet.name = rekeyNetReference(portNet.name, renamed);
	        }
	    }
	    for (const drawing of Object.values(design.drawings).flat()) {
	        rekeyDrawingNetReferences(drawing, renamed);
	    }
	    for (const net of (0, RefableNet_1.refableObjectValues)(design.nets)) {
	        if (!net.diffPair) {
	            continue;
	        }
	        if (typeof net.diffPair.positiveId === 'string') {
	            net.diffPair.positiveId = rekeyNetReference(net.diffPair.positiveId, renamed);
	        }
	        if (typeof net.diffPair.negativeId === 'string') {
	            net.diffPair.negativeId = rekeyNetReference(net.diffPair.negativeId, renamed);
	        }
	    }
	}
	function rekeyNetReference(name, renamed) {
	    const currentName = String(name);
	    return renamed.get(currentName) ?? currentName;
	}
	function rekeyDrawingNetReferences(drawing, renamed) {
	    if (hasDrawingNetReferences(drawing)) {
	        if (drawing.net !== undefined) {
	            drawing.net = rekeyNetReference(drawing.net, renamed);
	        }
	        for (const pointer of drawing.nets ?? []) {
	            pointer.n = rekeyNetReference(pointer.n, renamed);
	        }
	    }
	    if (drawing.type === 'group') {
	        for (const child of drawing.drawings) {
	            rekeyDrawingNetReferences(child, renamed);
	        }
	    }
	}
	
	return KicadHierarchyResolver;
}

var hasRequiredProject$2;

function requireProject$2 () {
	if (hasRequiredProject$2) return project$2;
	hasRequiredProject$2 = 1;
	Object.defineProperty(project$2, "__esModule", { value: true });
	project$2.joinKicadInstancePath = joinKicadInstancePath;
	project$2.kicadInstancePathWithoutRoot = kicadInstancePathWithoutRoot;
	project$2.parseProject = parseProject;
	project$2.normalizeDesignNetKeys = normalizeDesignNetKeys;
	const NodeParser_1 = requireNodeParser();
	const sigma_engine_shared_1 = requireDist$1();
	const board_1 = requireBoard$2();
	const ComponentResolver_1 = requireComponentResolver();
	const schematic_1 = requireSchematic$1();
	const DesignModels_1 = requireDesignModels();
	const Font_1 = requireFont();
	const RefableNet_1 = requireRefableNet();
	const ModelText_1 = requireModelText();
	const KicadHierarchyResolver_1 = requireKicadHierarchyResolver();
	function joinKicadInstancePath(instancePath, childSegment) {
	    const segments = [...instancePath.split('/'), childSegment].filter((segment) => segment.length > 0);
	    return `/${segments.join('/')}`;
	}
	function kicadInstancePathWithoutRoot(instancePath) {
	    const segments = instancePath.split('/').filter((segment) => segment.length > 0);
	    return `/${segments.slice(1).join('/')}`;
	}
	async function parseProject(def, opts = {}) {
	    if (def.type !== 'kicad') {
	        throw new Error(`Attempted to parse non-kicad project def: ${def.type}`);
	    }
	    if (typeof opts === 'boolean') {
	        opts = { removeNetRefs: opts };
	    }
	    const parser = new NodeParser_1.SExpressionFileParser();
	    const warnings = [];
	    const parsedDef = await preParseProjectFiles(def, parser, warnings);
	    const project = {
	        type: def.type,
	        modelVersion: ModelText_1.MODEL_TEXT_MIN_VERSION,
	        layerSets: {},
	        components: {},
	        designs: {},
	        warnings,
	        modules: [],
	        processingResults: {
	            type: 'project-processing',
	            message: 'Project processed successfully',
	            innerResults: [],
	        },
	        fonts: {
	            fonts: {},
	        },
	        globalNets: {},
	    };
	    if (def.name) {
	        project.name = def.name;
	    }
	    const resolver = opts?.componentResolverFactory?.(project, parsedDef) ?? new ComponentResolver_1.KicadComponentResolver(project);
	    const netRefCtx = new sigma_engine_shared_1.NetRefCtx();
	    const parsedImages = [];
	    for (const sheetInfo of parsedDef.kicadSchematics) {
	        if (!sheetInfo.contents) {
	            const schs = def.schematics[sheetInfo.filename];
	            if (!schs) {
	                project.warnings.push(`Could not find required input file: ${sheetInfo.filename}`);
	                continue;
	            }
	            sheetInfo.contents = await schs.contents.text();
	            sheetInfo.libContents = schs.lib;
	        }
	        const parsed = (0, schematic_1.parseSchematic)(parsedDef, parser, project, sheetInfo, resolver, netRefCtx, undefined, parsedImages);
	        netRefCtx.clearOldRefs();
	        project.designs[parsed.designId] = parsed;
	    }
	    for (const [brdId, brd] of Object.entries(def.boards)) {
	        const parsed = (0, board_1.parseBoard)(project, await brd.contents.text(), brdId, resolver, netRefCtx);
	        netRefCtx.clearOldRefs();
	        project.designs[parsed.designId] = parsed;
	    }
	    registerImageAssets(project, parsedImages, opts.extractedImages);
	    (0, KicadHierarchyResolver_1.resolveKicadHierarchyNets)(project);
	    normalizeDesignNetKeys(project);
	    (0, Font_1.packModelFonts)(project);
	    if (opts.removeNetRefs) {
	        return JSON.parse(JSON.stringify(project));
	    }
	    (0, DesignModels_1.computeAggragateMessageResultSeverity)(project.processingResults);
	    return project;
	}
	function registerImageAssets(project, parsedImages, sink) {
	    const byRef = new Map();
	    for (const image of parsedImages) {
	        if (!byRef.has(image.ref)) {
	            byRef.set(image.ref, image);
	        }
	    }
	    if (byRef.size === 0) {
	        return;
	    }
	    project.assets = [...(project.assets ?? [])];
	    for (const asset of byRef.values()) {
	        project.assets.push({
	            type: DesignModels_1.ProjectModelAssetType.Image,
	            ref: asset.ref,
	            name: asset.name,
	            mimeType: asset.mimeType,
	        });
	    }
	    sink?.push(...byRef.values());
	}
	function normalizeDesignNetKeys(project) {
	    for (const design of Object.values(project.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        const rebuilt = {};
	        const seen = new Set();
	        for (const netInfo of (0, RefableNet_1.refableObjectValues)(design.nets)) {
	            if (seen.has(netInfo)) {
	                continue;
	            }
	            seen.add(netInfo);
	            const name = String(netInfo.name);
	            const existing = rebuilt[name];
	            if (existing) {
	                rebuilt[name] = {
	                    ...existing,
	                    ...netInfo,
	                    name,
	                    metadata: {
	                        ...(existing.metadata ?? {}),
	                        ...(netInfo.metadata ?? {}),
	                    },
	                    netLabels: (existing.netLabels ?? []).concat(netInfo.netLabels ?? []),
	                    connections: existing.connections.concat(netInfo.connections.filter((c) => !existing.connections.some((e) => e.moduleRef === c.moduleRef && e.pinRef === c.pinRef))),
	                };
	            }
	            else {
	                rebuilt[name] = { ...netInfo, name };
	            }
	        }
	        design.nets = rebuilt;
	    }
	}
	async function preParseProjectFiles(def, parser, warnings) {
	    const kicadSchematics = await (0, schematic_1.preParseSchematicSheets)(def, parser, warnings);
	    return {
	        ...def,
	        kicadSchematics,
	    };
	}
	
	return project$2;
}

var v6ParseImage = {};

var imageAssets = {};

var base64$2 = {};

var hasRequiredBase64$1;

function requireBase64$1 () {
	if (hasRequiredBase64$1) return base64$2;
	hasRequiredBase64$1 = 1;
	Object.defineProperty(base64$2, "__esModule", { value: true });
	base64$2.uint8ArrayToBase64 = uint8ArrayToBase64;
	base64$2.base64ToUint8Array = base64ToUint8Array;
	function uint8ArrayToBase64(bytes) {
	    const nodeBuf = globalThis.Buffer;
	    if (nodeBuf) {
	        return nodeBuf.from(bytes).toString('base64');
	    }
	    const chunks = [];
	    const chunkSize = 0x8000;
	    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
	        chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + chunkSize)));
	    }
	    return btoa(chunks.join(''));
	}
	function base64ToUint8Array(base64) {
	    const nodeBuf = globalThis.Buffer;
	    if (nodeBuf) {
	        return new Uint8Array(nodeBuf.from(base64, 'base64'));
	    }
	    const binary = atob(base64);
	    const bytes = new Uint8Array(binary.length);
	    for (let i = 0; i < binary.length; i++) {
	        bytes[i] = binary.charCodeAt(i);
	    }
	    return bytes;
	}
	
	return base64$2;
}

var hasRequiredImageAssets;

function requireImageAssets () {
	if (hasRequiredImageAssets) return imageAssets;
	hasRequiredImageAssets = 1;
	Object.defineProperty(imageAssets, "__esModule", { value: true });
	imageAssets.detectImageMimeType = detectImageMimeType;
	imageAssets.decodeBase64Image = decodeBase64Image;
	imageAssets.mimeToExtension = mimeToExtension;
	imageAssets.computeImageRef = computeImageRef;
	imageAssets.readImagePixelSize = readImagePixelSize;
	const sigma_engine_shared_1 = requireDist$1();
	const base64_1 = requireBase64$1();
	function detectImageMimeType(data) {
	    if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47) {
	        return 'image/png';
	    }
	    if (data[0] === 0xff && data[1] === 0xd8) {
	        return 'image/jpeg';
	    }
	    if (data[0] === 0x42 && data[1] === 0x4d) {
	        return 'image/bmp';
	    }
	    return 'application/octet-stream';
	}
	function decodeBase64Image(base64) {
	    const cleaned = base64.replace(/\s/g, '');
	    const data = (0, base64_1.base64ToUint8Array)(cleaned);
	    const mimeType = detectImageMimeType(data);
	    return { data, mimeType };
	}
	function mimeToExtension(mimeType) {
	    switch (mimeType) {
	        case 'image/png':
	            return 'png';
	        case 'image/jpeg':
	            return 'jpg';
	        case 'image/bmp':
	            return 'bmp';
	        default:
	            return 'bin';
	    }
	}
	function computeImageRef(sourceIdentifier, mimeType) {
	    const hash = sigma_engine_shared_1.Utils.fnv1aHash(sourceIdentifier);
	    const ext = mimeToExtension(mimeType);
	    return `images/${hash}.${ext}`;
	}
	function readImagePixelSize(data, mimeType) {
	    switch (mimeType) {
	        case 'image/png':
	            return readPngPixelSize(data);
	        case 'image/jpeg':
	            return readJpegPixelSize(data);
	        case 'image/bmp':
	            return readBmpPixelSize(data);
	        default:
	            return null;
	    }
	}
	function readPngPixelSize(data) {
	    const IHDR_END = 24;
	    if (data.length < IHDR_END) {
	        return null;
	    }
	    const isIhdr = data[12] === 0x49 && data[13] === 0x48 && data[14] === 0x44 && data[15] === 0x52;
	    if (!isIhdr) {
	        return null;
	    }
	    const width = readUint32BE(data, 16);
	    const height = readUint32BE(data, 20);
	    return width > 0 && height > 0 ? { width, height } : null;
	}
	const JPEG_SOF_MARKERS = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
	function readJpegPixelSize(data) {
	    let offset = 2;
	    while (offset + 9 < data.length) {
	        if (data[offset] !== 0xff) {
	            offset++;
	            continue;
	        }
	        const marker = data[offset + 1];
	        if (marker === 0xff) {
	            offset++;
	            continue;
	        }
	        if (JPEG_SOF_MARKERS.has(marker)) {
	            const height = (data[offset + 5] << 8) | data[offset + 6];
	            const width = (data[offset + 7] << 8) | data[offset + 8];
	            return width > 0 && height > 0 ? { width, height } : null;
	        }
	        const segmentLength = (data[offset + 2] << 8) | data[offset + 3];
	        if (segmentLength < 2) {
	            return null;
	        }
	        offset += 2 + segmentLength;
	    }
	    return null;
	}
	function readBmpPixelSize(data) {
	    const DIB_HEADER_OFFSET = 14;
	    const CORE_HEADER_SIZE = 12;
	    const INFO_HEADER_SIZE = 40;
	    if (data.length < DIB_HEADER_OFFSET + 4) {
	        return null;
	    }
	    const headerSize = readUint32LE(data, DIB_HEADER_OFFSET);
	    if (data.length < DIB_HEADER_OFFSET + headerSize) {
	        return null;
	    }
	    if (headerSize === CORE_HEADER_SIZE) {
	        const width = readUint16LE(data, 18);
	        const height = readUint16LE(data, 20);
	        return width > 0 && height > 0 ? { width, height } : null;
	    }
	    if (headerSize >= INFO_HEADER_SIZE) {
	        const width = readInt32LE(data, 18);
	        const height = Math.abs(readInt32LE(data, 22));
	        return width > 0 && height > 0 ? { width, height } : null;
	    }
	    return null;
	}
	function readUint32BE(data, offset) {
	    return ((data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3]) >>> 0;
	}
	function readUint32LE(data, offset) {
	    return (data[offset] | (data[offset + 1] << 8) | (data[offset + 2] << 16) | (data[offset + 3] << 24)) >>> 0;
	}
	function readInt32LE(data, offset) {
	    return readUint32LE(data, offset) | 0;
	}
	function readUint16LE(data, offset) {
	    return data[offset] | (data[offset + 1] << 8);
	}
	
	return imageAssets;
}

var hasRequiredV6ParseImage;

function requireV6ParseImage () {
	if (hasRequiredV6ParseImage) return v6ParseImage;
	hasRequiredV6ParseImage = 1;
	Object.defineProperty(v6ParseImage, "__esModule", { value: true });
	v6ParseImage.parseImage = parseImage;
	const Util_1 = requireUtil();
	const imageAssets_1 = requireImageAssets();
	const KICAD_IMAGE_DPI = 300;
	const MM_PER_INCH = 25.4;
	function parseImage(node, warnings) {
	    let center = [0, 0];
	    let scale = 1;
	    let uuid = '';
	    const dataChunks = [];
	    for (const child of node.nodes) {
	        switch (child.name) {
	            case 'at': {
	                center = [parseFloat(child.values[0]), parseFloat(child.values[1])];
	                break;
	            }
	            case 'scale': {
	                const parsed = parseFloat(child.values[0]);
	                if (Number.isFinite(parsed) && parsed > 0) {
	                    scale = parsed;
	                }
	                break;
	            }
	            case 'uuid': {
	                uuid = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	                break;
	            }
	            case 'data': {
	                dataChunks.push(...child.values.map((value) => (0, Util_1.removeQuotes)(value)));
	                break;
	            }
	        }
	    }
	    const label = uuid.length > 0 ? uuid : '(no uuid)';
	    if (!Number.isFinite(center[0]) || !Number.isFinite(center[1])) {
	        warnings.push(`Skipped schematic image ${label}: its position could not be read`);
	        return null;
	    }
	    const base64 = dataChunks.join('');
	    if (base64.length === 0) {
	        warnings.push(`Skipped schematic image ${label}: it carries no data`);
	        return null;
	    }
	    let data;
	    let mimeType;
	    try {
	        ({ data, mimeType } = (0, imageAssets_1.decodeBase64Image)(base64));
	    }
	    catch {
	        warnings.push(`Skipped schematic image ${label}: its data is not valid base64`);
	        return null;
	    }
	    if (mimeType === 'application/octet-stream') {
	        warnings.push(`Skipped schematic image ${label}: its format is not recognised (PNG, JPEG and BMP are supported)`);
	        return null;
	    }
	    const pixelSize = (0, imageAssets_1.readImagePixelSize)(data, mimeType);
	    if (!pixelSize) {
	        warnings.push(`Skipped schematic image ${label}: its ${mimeType} header does not hold readable dimensions`);
	        return null;
	    }
	    const width = (pixelSize.width * MM_PER_INCH * scale) / KICAD_IMAGE_DPI;
	    const height = (pixelSize.height * MM_PER_INCH * scale) / KICAD_IMAGE_DPI;
	    const ref = (0, imageAssets_1.computeImageRef)(base64, mimeType);
	    return {
	        drawing: {
	            type: 'image',
	            pos: [center[0] - width / 2, center[1] - height / 2],
	            width,
	            height,
	            imageRef: ref,
	        },
	        asset: {
	            ref,
	            name: uuid.length > 0 ? uuid : ref,
	            data,
	            mimeType,
	        },
	    };
	}
	
	return v6ParseImage;
}

var v6ExtractSheetInfo = {};

var hasRequiredV6ExtractSheetInfo;

function requireV6ExtractSheetInfo () {
	if (hasRequiredV6ExtractSheetInfo) return v6ExtractSheetInfo;
	hasRequiredV6ExtractSheetInfo = 1;
	Object.defineProperty(v6ExtractSheetInfo, "__esModule", { value: true });
	v6ExtractSheetInfo.extractSheetInfo = extractSheetInfo;
	const Util_1 = requireUtil();
	function extractSheetInfo(node) {
	    let start = [0, 0];
	    let size = [0, 0];
	    let sheetname;
	    let sheetfile;
	    let uuid;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'at': {
	                start = [parseFloat(n.values[0]), parseFloat(n.values[1])];
	                break;
	            }
	            case 'size': {
	                size = [parseFloat(n.values[0]), parseFloat(n.values[1])];
	                break;
	            }
	            case 'uuid': {
	                uuid = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	            case 'property': {
	                switch (n.values[0].toLowerCase()) {
	                    case '"sheetname"':
	                        sheetname = (0, Util_1.removeQuotes)(n.values[1]);
	                        break;
	                    case '"sheetfile"':
	                        sheetfile = (0, Util_1.removeQuotes)(n.values[1]);
	                        break;
	                }
	                break;
	            }
	        }
	    });
	    return {
	        start,
	        size,
	        sheetname,
	        sheetfile,
	        uuid,
	    };
	}
	
	return v6ExtractSheetInfo;
}

var schematicUtils = {};

var hasRequiredSchematicUtils;

function requireSchematicUtils () {
	if (hasRequiredSchematicUtils) return schematicUtils;
	hasRequiredSchematicUtils = 1;
	Object.defineProperty(schematicUtils, "__esModule", { value: true });
	schematicUtils.addSchematicDrawing = addSchematicDrawing;
	const Utils_1 = requireUtils$4();
	function addSchematicDrawing(model, layer, drawing) {
	    if (!model.drawings[layer]) {
	        model.drawings[layer] = [...(0, Utils_1.toArray)(drawing)];
	    }
	    else {
	        model.drawings[layer].push(...(0, Utils_1.toArray)(drawing));
	    }
	}
	
	return schematicUtils;
}

var nodeIsHidden = {};

var textBoolean = {};

var hasRequiredTextBoolean;

function requireTextBoolean () {
	if (hasRequiredTextBoolean) return textBoolean;
	hasRequiredTextBoolean = 1;
	Object.defineProperty(textBoolean, "__esModule", { value: true });
	textBoolean.textBoolean = textBoolean$1;
	function textBoolean$1(value, defaultValue = false) {
	    switch (value?.toLowerCase()) {
	        case 'yes':
	        case 'on':
	            return true;
	        case 'no':
	        case 'off':
	            return false;
	        default:
	            return defaultValue;
	    }
	}
	
	return textBoolean;
}

var hasRequiredNodeIsHidden;

function requireNodeIsHidden () {
	if (hasRequiredNodeIsHidden) return nodeIsHidden;
	hasRequiredNodeIsHidden = 1;
	Object.defineProperty(nodeIsHidden, "__esModule", { value: true });
	nodeIsHidden.nodeIsHidden = nodeIsHidden$1;
	const textBoolean_1 = requireTextBoolean();
	function nodeIsHidden$1(node) {
	    if (node.values.includes('hide')) {
	        return true;
	    }
	    const hideNode = node.nodes.find((n) => n.name === 'hide');
	    return hideNode ? (0, textBoolean_1.textBoolean)(hideNode.values[0]) : false;
	}
	
	return nodeIsHidden;
}

var kicadSymbolParser = {};

var hasRequiredKicadSymbolParser;

function requireKicadSymbolParser () {
	if (hasRequiredKicadSymbolParser) return kicadSymbolParser;
	hasRequiredKicadSymbolParser = 1;
	Object.defineProperty(kicadSymbolParser, "__esModule", { value: true });
	kicadSymbolParser.KicadSymbolNameUtils = kicadSymbolParser.KicadSymbolNameParser = void 0;
	class KicadSymbolNameParser {
	    static parseLibId(libId) {
	        const colonIndex = libId.indexOf(':');
	        if (colonIndex !== -1) {
	            return {
	                libraryName: libId.substring(0, colonIndex),
	                symbolName: libId.substring(colonIndex + 1),
	                originalLibId: libId,
	            };
	        }
	        else {
	            return {
	                symbolName: libId,
	                originalLibId: libId,
	            };
	        }
	    }
	    static parseSymbolName(symbolName) {
	        const libIdInfo = this.parseLibId(symbolName);
	        const nameToProcess = libIdInfo.symbolName;
	        const subSymbolMatch = nameToProcess.match(/^(.+)_(\d+)_(\d+)$/);
	        if (subSymbolMatch) {
	            const rootName = subSymbolMatch[1];
	            const unit = parseInt(subSymbolMatch[2], 10);
	            const bodyStyle = parseInt(subSymbolMatch[3], 10);
	            const rootInfo = this.parseRootSymbolName(rootName);
	            return {
	                ...rootInfo,
	                isSubSymbol: true,
	                unit,
	                bodyStyle,
	                originalName: symbolName,
	            };
	        }
	        else {
	            const rootInfo = this.parseRootSymbolName(nameToProcess);
	            return {
	                ...rootInfo,
	                isSubSymbol: false,
	                originalName: symbolName,
	            };
	        }
	    }
	    static parseRootSymbolName(rootName) {
	        const parts = rootName.split('_');
	        if (parts.length < 3) {
	            return {
	                sheetName: undefined,
	                orientation: undefined,
	                isMirrored: false,
	                libraryReference: rootName,
	                displayMode: undefined,
	            };
	        }
	        let partIndex = 0;
	        const sheetName = parts[partIndex++];
	        const orientationStr = parts[partIndex++];
	        const orientation = parseInt(orientationStr, 10);
	        if (isNaN(orientation)) {
	            return {
	                sheetName: undefined,
	                orientation: undefined,
	                isMirrored: false,
	                libraryReference: rootName,
	                displayMode: undefined,
	            };
	        }
	        let isMirrored = false;
	        if (partIndex < parts.length && parts[partIndex] === 'mirrored') {
	            isMirrored = true;
	            partIndex++;
	        }
	        if (partIndex >= parts.length) {
	            throw new Error(`Invalid root symbol name format: ${rootName}. Missing library reference.`);
	        }
	        const remainingParts = parts.slice(partIndex);
	        let displayMode;
	        let libraryReferenceParts = remainingParts;
	        if (remainingParts.length > 1) {
	            const lastPart = remainingParts[remainingParts.length - 1];
	            const lastPartAsNumber = parseInt(lastPart, 10);
	            if (!isNaN(lastPartAsNumber)) {
	                displayMode = lastPartAsNumber;
	                libraryReferenceParts = remainingParts.slice(0, -1);
	            }
	        }
	        const libraryReference = libraryReferenceParts.join('_');
	        return {
	            sheetName,
	            orientation,
	            isMirrored,
	            libraryReference,
	            displayMode,
	        };
	    }
	    static reconstructSymbolName(info) {
	        if (info.sheetName === undefined || info.orientation === undefined) {
	            let name = info.libraryReference;
	            if (info.isSubSymbol && info.unit !== undefined && info.bodyStyle !== undefined) {
	                name += `_${info.unit}_${info.bodyStyle}`;
	            }
	            return name;
	        }
	        let name = info.sheetName;
	        name += `_${info.orientation}`;
	        if (info.isMirrored) {
	            name += '_mirrored';
	        }
	        name += `_${info.libraryReference}`;
	        if (info.displayMode !== undefined) {
	            name += `_${info.displayMode}`;
	        }
	        if (info.isSubSymbol && info.unit !== undefined && info.bodyStyle !== undefined) {
	            name += `_${info.unit}_${info.bodyStyle}`;
	        }
	        return name;
	    }
	    static getRootSymbolName(symbolName) {
	        const info = this.parseSymbolName(symbolName);
	        return this.reconstructSymbolName({
	            ...info,
	            isSubSymbol: false,
	            unit: undefined,
	            bodyStyle: undefined,
	        });
	    }
	}
	kicadSymbolParser.KicadSymbolNameParser = KicadSymbolNameParser;
	class KicadSymbolNameUtils {
	    static isSubSymbol(symbolName) {
	        try {
	            return KicadSymbolNameParser.parseSymbolName(symbolName).isSubSymbol;
	        }
	        catch {
	            return false;
	        }
	    }
	    static generateSubSymbolNames(rootSymbolName, maxUnits = 4, maxBodyStyles = 2) {
	        const subSymbols = [];
	        for (let unit = 0; unit <= maxUnits; unit++) {
	            for (let bodyStyle = 0; bodyStyle < maxBodyStyles; bodyStyle++) {
	                subSymbols.push(`${rootSymbolName}_${unit}_${bodyStyle}`);
	            }
	        }
	        return subSymbols;
	    }
	    static getOrientationDescription(orientation) {
	        switch (orientation) {
	            case 0:
	                return 'rightwards (0°)';
	            case 1:
	                return 'upwards (90°)';
	            case 2:
	                return 'leftwards (180°)';
	            case 3:
	                return 'downwards (270°)';
	            default:
	                return `unknown (${orientation})`;
	        }
	    }
	}
	kicadSymbolParser.KicadSymbolNameUtils = KicadSymbolNameUtils;
	
	return kicadSymbolParser;
}

var KicadNetCombobulator = {};

var hasRequiredKicadNetCombobulator;

function requireKicadNetCombobulator () {
	if (hasRequiredKicadNetCombobulator) return KicadNetCombobulator;
	hasRequiredKicadNetCombobulator = 1;
	Object.defineProperty(KicadNetCombobulator, "__esModule", { value: true });
	KicadNetCombobulator.KicadNetCombobulator = void 0;
	const NetCombobulator_1 = requireNetCombobulator();
	const KicadNetNamingStrategy_1 = requireKicadNetNamingStrategy();
	class KicadNetCombobulator$1 extends NetCombobulator_1.NetCombobulator {
	    constructor(netRefCtx, opts = {}, namingStrategy = new KicadNetNamingStrategy_1.KicadNetNamingStrategy()) {
	        super(netRefCtx, namingStrategy, undefined, opts);
	    }
	}
	KicadNetCombobulator.KicadNetCombobulator = KicadNetCombobulator$1;
	
	return KicadNetCombobulator;
}

var hasRequiredV6schema;

function requireV6schema () {
	if (hasRequiredV6schema) return v6schema;
	hasRequiredV6schema = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.KICAD_PIN_ELECTRICAL_TYPES = void 0;
		exports.parseSchematic = parseSchematic;
		exports.kicadAddNetDifferentialPairInfo = kicadAddNetDifferentialPairInfo;
		exports.setLayerSelector = setLayerSelector;
		exports.preParseSchematicSheet = preParseSchematicSheet;
		exports.shouldStoreKicadSourceReference = shouldStoreKicadSourceReference;
		exports.isAnnotatedKicadModuleName = isAnnotatedKicadModuleName;
		exports.getShouldAnnotateModuleName = getShouldAnnotateModuleName;
		exports.parseText = parseText;
		exports.parseSheetPins = parseSheetPins;
		exports.parsePolyline = parsePolyline;
		exports.parseComponents = parseComponents;
		exports.parseComponentDefinition = parseComponentDefinition;
		exports.parseSymbol = parseSymbol;
		exports.combineNets = combineNets;
		const Area_1 = requireArea();
		const layers_1 = requireLayers$2();
		const constants_1 = requireConstants();
		const Util_1 = requireUtil();
		const project_1 = requireProject$2();
		const v6ParseArc_1 = requireV6ParseArc();
		const v6ParseImage_1 = requireV6ParseImage();
		const v6ParseRectangle_1 = requireV6ParseRectangle();
		const v6ParseLabel_1 = requireV6ParseLabel();
		const v6ExtractPolygon_1 = requireV6ExtractPolygon();
		const v6ParseTitleBlock_1 = requireV6ParseTitleBlock();
		const v6ParseCircle_1 = requireV6ParseCircle();
		const v6Common_1 = requireV6Common();
		const v6ExtractSheetInfo_1 = requireV6ExtractSheetInfo();
		const sigma_engine_shared_1 = requireDist$1();
		const RefableNet_1 = requireRefableNet();
		const DesignModels_1 = requireDesignModels();
		const Utils_1 = requireUtils$4();
		const Transform_1 = requireTransform();
		const fonts_1 = requireFonts();
		const schematicUtils_1 = requireSchematicUtils();
		const sigma_engine_shared_2 = requireDist$1();
		const Constants_1 = requireConstants$5();
		const nodeIsHidden_1 = requireNodeIsHidden();
		const parseTextTransforms_1 = requireParseTextTransforms();
		const getMirrorValuesToKeepTextRightSideUp_1 = requireGetMirrorValuesToKeepTextRightSideUp();
		const kicadSymbolParser_1 = requireKicadSymbolParser();
		const KicadNetCombobulator_1 = requireKicadNetCombobulator();
		const KicadNetNamingStrategy_1 = requireKicadNetNamingStrategy();
		const KicadHierarchyResolver_1 = requireKicadHierarchyResolver();
		const KicadBusTopology_1 = requireKicadBusTopology();
		const DEFAULT_LINE_WIDTH = 0.1;
		let moduleNames;
		let layerSelector;
		function parseSchematic(def, parser, project, sch, resolver, netRefCtx, netCombobulationOpts, extractedImages) {
		    if (project.type !== 'kicad') {
		        throw new Error('Cannot parse kicad schematic to non-kicad project');
		    }
		    moduleNames = new Set();
		    layerSelector = new layers_1.KicadSchLayerSelector();
		    const parsedPage = typeof sch.page === 'string' ? parseInt(sch.page, 10) : sch.page;
		    const sheetIndex = typeof parsedPage === 'number' && Number.isFinite(parsedPage) ? parsedPage : sch.numberingOrder;
		    const model = {
		        modelType: 'schematic',
		        designId: sch.uuid,
		        designName: sch.name,
		        sourceFile: sch.filename,
		        grid: v6Common_1.DefaultSchGrid,
		        sheetIndex: (0, Utils_1.max)(sheetIndex, 0),
		        edges_bbox: {
		            minx: Number.POSITIVE_INFINITY,
		            miny: Number.POSITIVE_INFINITY,
		            maxx: Number.NEGATIVE_INFINITY,
		            maxy: Number.NEGATIVE_INFINITY,
		        },
		        initial_zoombox: {
		            minx: Number.POSITIVE_INFINITY,
		            miny: Number.POSITIVE_INFINITY,
		            maxx: Number.NEGATIVE_INFINITY,
		            maxy: Number.NEGATIVE_INFINITY,
		        },
		        font: {
		            default: fonts_1.Font.Newstroke,
		        },
		        layerSet: layers_1.KICAD_SCH_LAYERSET,
		        drawings: {},
		        modules: [],
		        ports: {},
		        nets: (0, RefableNet_1.refableObject)({}, netRefCtx, (r, n1, n2) => combineNets(r, n1, n2)),
		        warnings: [],
		        metadata: {},
		    };
		    const warnings = model.warnings;
		    if (!project.layerSets[layers_1.KICAD_SCH_LAYERSET]) {
		        project.layerSets[layers_1.KICAD_SCH_LAYERSET] = (0, layers_1.getKicadSchLayers)().map((layer) => {
		            layer.defaultVisible = typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true;
		            return layer;
		        });
		    }
		    const tree = parser.parseFile(sch.filename, sch.contents);
		    const globalDriverNames = new Set();
		    const netCalc = new KicadNetCombobulator_1.KicadNetCombobulator(netRefCtx, netCombobulationOpts, new KicadNetNamingStrategy_1.KicadNetNamingStrategy(sch.netPath, globalDriverNames));
		    const busTopology = new KicadBusTopology_1.KicadBusTopology();
		    const busAliases = new Map();
		    const wires = [];
		    const localLabels = [];
		    const symbolNodes = [];
		    const sheetNodes = [];
		    const hierarchicalLabelNodes = [];
		    tree.nodes.forEach((node) => {
		        switch (node.name) {
		            case 'lib_symbols': {
		                parseComponents(project, node, netRefCtx, resolver);
		                break;
		            }
		            case 'junction': {
		                const junction = parseJunction(node);
		                busTopology.addJunction(junction.position);
		                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Junction).id, junction);
		                break;
		            }
		            case 'no_connect': {
		                const position = parseNoConnectPosition(node);
		                if (position) {
		                    netCalc.namingStrategy.addNoConnectMarker(position, netCalc.pointRoundingPrecision / 2);
		                }
		                break;
		            }
		            case 'wire': {
		                const wire = parseWire(node);
		                wires.push(wire);
		                netCalc.addWire({
		                    from: wire.start,
		                    to: wire.end,
		                });
		                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Wire).id, wire);
		                break;
		            }
		            case 'bus': {
		                const bus = parseWire(node);
		                busTopology.addTrack(bus);
		                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Bus).id, bus);
		                break;
		            }
		            case 'bus_entry': {
		                const wire = parseBusEntry(node);
		                wires.push(wire);
		                netCalc.addWire({
		                    from: wire.start,
		                    to: wire.end,
		                });
		                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Wire).id, wire);
		                break;
		            }
		            case 'image': {
		                const image = (0, v6ParseImage_1.parseImage)(node, warnings);
		                if (image) {
		                    (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Image).id, image.drawing);
		                    extractedImages?.push(image.asset);
		                }
		                break;
		            }
		            case 'polyline': {
		                const segments = parsePolyline(node, false);
		                for (const segment of segments) {
		                    (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Other).id, segment);
		                }
		                break;
		            }
		            case 'global_label': {
		                const label = parseGlobalLabel(node, netRefCtx, warnings);
		                const labelValue = KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(label.value.valueOf());
		                globalDriverNames.add(labelValue);
		                const netRef = netCalc.setLabel(label.pos, labelValue, 'global');
		                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).id, label);
		                label.net = netRef.strCast();
		                break;
		            }
		            case 'text': {
		                const text = (0, v6ParseLabel_1.parseTextElement)(node, false, warnings, undefined, layerSelector);
		                (0, schematicUtils_1.addSchematicDrawing)(model, text.layers[0], text.drawing);
		                break;
		            }
		            case 'text_box': {
		                const textBox = (0, v6ParseLabel_1.parseTextBox)(node, false, warnings, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Other).name, layerSelector);
		                (0, schematicUtils_1.addSchematicDrawing)(model, textBox.layers[0], textBox.drawing);
		                break;
		            }
		            case 'label': {
		                {
		                    const label = (0, v6ParseLabel_1.parseLabel)(model, node, false, warnings, netRefCtx, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).name, layerSelector, resolver, true);
		                    if (label.drawing.value.valueOf() !== '') {
		                        if (KicadBusTopology_1.KicadBusTopology.isBusLabel(label.drawing.value.valueOf())) {
		                            busTopology.addDriver(label.drawing.value.valueOf(), KicadBusTopology_1.KicadBusDriverType.LocalLabel, label.drawing.pos);
		                        }
		                        else {
		                            localLabels.push(label.drawing);
		                        }
		                    }
		                    (0, schematicUtils_1.addSchematicDrawing)(model, label.layers[0], label.drawing);
		                    break;
		                }
		            }
		            case 'hierarchical_label': {
		                hierarchicalLabelNodes.push(node);
		                break;
		            }
		            case 'bus_alias': {
		                const aliasName = (0, Util_1.removeQuotes)(node.values[0] ?? '');
		                const membersNode = node.nodes.find((child) => child.name === 'members');
		                if (aliasName.length > 0 && membersNode) {
		                    busAliases.set(aliasName, membersNode.values.map((member) => (0, Util_1.removeQuotes)(member)));
		                }
		                break;
		            }
		            case 'uuid': {
		                const value = (0, Util_1.cleanMetadataVal)(node.values[0]);
		                if (value) {
		                    model.metadata['kicad.uuid'] = value;
		                }
		                break;
		            }
		            case 'sheet': {
		                sheetNodes.push(node);
		                break;
		            }
		            case 'symbol': {
		                symbolNodes.push(node);
		                break;
		            }
		            case 'title_block': {
		                (0, v6ParseTitleBlock_1.parseTitleBlock)(model, node);
		                break;
		            }
		            case 'rectangle': {
		                const drawings = (0, v6ParseRectangle_1.parseRectangle)(node, false, warnings, DesignModels_1.SchLayerType.Other, layerSelector, Constants_1.SEGMENT_MIN_WIDTH);
		                for (const drawing of drawings) {
		                    for (const layer of drawing.layers) {
		                        (0, schematicUtils_1.addSchematicDrawing)(model, layer, drawing.drawing);
		                    }
		                }
		                break;
		            }
		            case 'circle': {
		                const obj = (0, v6ParseCircle_1.readCircle)(node, layerSelector.getLayerByType(DesignModels_1.SchLayerType.Other).name);
		                const layers = layerSelector.filterLayers(obj.layer);
		                (0, schematicUtils_1.addSchematicDrawing)(model, layers[0].id, obj.circle);
		                break;
		            }
		            case 'arc': {
		                const drawing = (0, v6ParseArc_1.parseArc)(false, node, false, warnings, layerSelector.getLayerByType(DesignModels_1.SchLayerType.Other).name, layerSelector);
		                if (drawing) {
		                    for (const layer of drawing.layers) {
		                        (0, schematicUtils_1.addSchematicDrawing)(model, layer, drawing.drawing);
		                    }
		                }
		                break;
		            }
		        }
		    });
		    symbolNodes.forEach((node) => {
		        switch (node.name) {
		            case 'symbol': {
		                const m = parseModule(project, node, warnings, model, netCalc, resolver, {
		                    globalDriverNames,
		                    instancePath: sch.instancePath,
		                    annotationPath: sch.annotationPath,
		                    projectName: sch.projectName,
		                    legacySymbolInstances: sch.legacySymbolInstances,
		                    sourceInstanceCount: sch.sourceInstanceCount,
		                });
		                resolver.addComponentPart(m);
		                model.modules.push(m);
		                break;
		            }
		        }
		    });
		    for (const node of hierarchicalLabelNodes) {
		        const rawValue = (0, Util_1.removeQuotes)(node.values[0] ?? '');
		        const label = parseHierarchicalLabel(model, node, netCalc, warnings, resolver);
		        if (KicadBusTopology_1.KicadBusTopology.isBusLabel(rawValue)) {
		            busTopology.addDriver(rawValue, KicadBusTopology_1.KicadBusDriverType.HierarchicalLabel, label.pos);
		        }
		        (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).id, label);
		    }
		    for (const label of localLabels) {
		        const labelValue = KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(label.value.valueOf());
		        const netRef = netCalc.setLabel(label.pos, globalDriverNames.has(labelValue) ? labelValue : sch.netPath + labelValue, 'local_label');
		        label.net = netRef.strCast();
		    }
		    sheetNodes.forEach((node, sheetSymbolIndex) => {
		        switch (node.name) {
		            case 'sheet': {
		                const sheet = parseSheet(def, project, model, sch, node, sheetSymbolIndex + 1, warnings, netCalc, resolver);
		                const component = project.components[sheet.drawModule.componentRef];
		                const symbol = component?.symbols.find((candidate) => candidate.ref === sheet.drawModule.footprintRef);
		                const pinNodes = node.nodes.filter((child) => child.name === 'pin');
		                for (let index = 0; index < pinNodes.length; index++) {
		                    const pinNode = pinNodes[index];
		                    const pin = symbol?.pins[index];
		                    const pinName = (0, Util_1.removeQuotes)(pinNode.values[0] ?? '');
		                    const atNode = pinNode.nodes.find((child) => child.name === 'at');
		                    if (!pin || !atNode || !KicadBusTopology_1.KicadBusTopology.isBusLabel(pinName)) {
		                        continue;
		                    }
		                    busTopology.addDriver(pinName, KicadBusTopology_1.KicadBusDriverType.SheetPin, [parseFloat(atNode.values[0]), parseFloat(atNode.values[1])], sheet.drawModule.ref, pin.ref);
		                }
		                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id, sheet);
		                break;
		            }
		        }
		    });
		    (0, KicadHierarchyResolver_1.recordKicadBusAliases)(project, busAliases, sch.netPath);
		    (0, KicadHierarchyResolver_1.recordKicadBusSheet)(model, busTopology.build());
		    const nets = netCalc.computeNets();
		    const resolvedPinHintNames = netCalc.namingStrategy.resolvePinHintNetNames(nets.values());
		    const netNames = new Set(Array.from(nets.keys(), (name) => resolvedPinHintNames.get(name) ?? name));
		    for (const net of nets.values()) {
		        const computedNetName = String(net.name.strCast());
		        const baseNetName = resolvedPinHintNames.get(computedNetName) ?? computedNetName;
		        let netName = baseNetName;
		        let suffix = 1;
		        while (model.nets[netName]) {
		            netName = `${baseNetName}_${suffix++}`;
		        }
		        for (const netRef of net.netRefs) {
		            netRef.setName(netName);
		        }
		        for (const connection of net.connections) {
		            const driver = connection.connection.appliesNetName;
		            if (driver && (driver.labelType === 'pin_hint' || driver.label === baseNetName)) {
		                driver.label = netName;
		            }
		        }
		        const isGlobal = net.labels.some((l) => l.labelType === 'global' || l.labelType === 'power_pin');
		        const isHierarchical = net.labels.some((l) => l.labelType === 'hier_label');
		        const netInfo = {
		            name: net.name.strCast(),
		            connections: [],
		            isGlobal,
		            isHierarchical,
		            netLabels: net.labels.map((l) => ({
		                type: l.labelType,
		                position: l.position,
		                label: l.originalLabel,
		                isGlobal: l.labelType === 'global' || l.labelType === 'power_pin',
		                isHierarchical: l.labelType === 'hier_label',
		            })),
		        };
		        (0, KicadHierarchyResolver_1.recordKicadNetDrivers)(netInfo, net, sch.netPath);
		        model.nets[netName] = netInfo;
		        for (const connection of net.connections) {
		            const module = model.modules.find((m) => m.ref === connection.connection.moduleRef);
		            if (!module) {
		                const schRef = model.drawings[(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id]?.find((d) => d.type === 'schref' && d.drawModule.ref === connection.connection.moduleRef);
		                if (schRef) {
		                    warnings.push(`Net connections to schematic refs not yet supported: ${net.name.toString()} => ${schRef.name}::${connection.connection.pinRef}`);
		                    continue;
		                }
		                throw new Error('Internal consistency error in parsing net module connections');
		            }
		            module.pinNets[connection.connection.pinRef] = netName;
		            if (!model.nets[netName].connections.find((c) => c.moduleRef === connection.connection.moduleRef && c.pinRef === connection.connection.pinRef)) {
		                model.nets[netName].connections.push(connection.connection);
		            }
		        }
		        kicadAddNetDifferentialPairInfo(netInfo.name, netInfo, netNames, netRefCtx, warnings);
		    }
		    wires.forEach((wire) => {
		        const net = netCalc.getNetAtPosition(wire.start) || netCalc.getNetAtPosition(wire.end);
		        if (net && net.netRefs.size > 0) {
		            const netRef = Array.from(net.netRefs)[0];
		            wire.net = netRef.strCast();
		        }
		    });
		    const bbox = new Area_1.default();
		    bbox.extendFromSchModel(project, model, project.layerSets[layers_1.KICAD_SCH_LAYERSET], warnings);
		    model.edges_bbox = {
		        minx: bbox.xMin,
		        miny: bbox.yMin,
		        maxx: bbox.xMax,
		        maxy: bbox.yMax,
		    };
		    const visibleBbox = new Area_1.default();
		    visibleBbox.extendFromSchModel(project, model, project.layerSets[layers_1.KICAD_SCH_LAYERSET], model.warnings, false);
		    model.initial_zoombox = {
		        minx: visibleBbox.xMin,
		        miny: visibleBbox.yMin,
		        maxx: visibleBbox.xMax,
		        maxy: visibleBbox.yMax,
		    };
		    model.warnings = warnings;
		    (0, Util_1.dedupeWarnings)(model);
		    moduleNames = undefined;
		    layerSelector = undefined;
		    return model;
		}
		function kicadAddNetDifferentialPairInfo(netName, netInfo, netNames, netRefCtx, warnings) {
		    if (netInfo) {
		        netInfo.name = netName;
		    }
		    const { diffPairName, currentIsPositive, diffPairComplementName } = getDiffPairName(netName);
		    if (diffPairName) {
		        netInfo.diffPair = {
		            pairName: diffPairName,
		            positiveId: netRefCtx.existingOrNewRef(currentIsPositive ? netName : diffPairComplementName).strCast(),
		            negativeId: netRefCtx.existingOrNewRef(currentIsPositive ? diffPairComplementName : netName).strCast(),
		        };
		        if ((0, sigma_engine_shared_1.isNetRef)(netInfo.name)) {
		            netInfo.name.on('change', (newVal) => {
		                const { diffPairName, currentIsPositive, diffPairComplementName } = getDiffPairName(newVal.strCast());
		                netInfo.diffPair.pairName = diffPairName;
		                netInfo.diffPair.positiveId = currentIsPositive ? newVal.strCast() : diffPairComplementName;
		                netInfo.diffPair.negativeId = currentIsPositive ? diffPairComplementName : newVal.strCast();
		            });
		        }
		        if (!netNames.has(netInfo.diffPair.positiveId.toString())) {
		            const lowerSearch = netInfo.diffPair.positiveId.toString().toLowerCase();
		            let found = false;
		            for (const net of netNames) {
		                if (net.toLowerCase() === lowerSearch) {
		                    netInfo.diffPair.positiveId = net;
		                    found = true;
		                    break;
		                }
		            }
		            if (!found) {
		                warnings.push(`Could not find positive net for differential pair '${netInfo.diffPair.pairName}'`);
		            }
		        }
		        if (!netNames.has(netInfo.diffPair.negativeId.toString())) {
		            const lowerSearch = netInfo.diffPair.negativeId.toString().toLowerCase();
		            let found = false;
		            for (const net of netNames) {
		                if (net.toLowerCase() === lowerSearch) {
		                    netInfo.diffPair.negativeId = net;
		                    found = true;
		                    break;
		                }
		            }
		            if (!found) {
		                warnings.push(`Could not find negative net for differential pair '${netInfo.diffPair.pairName}'`);
		            }
		        }
		    }
		}
		function getDiffPairName(netName) {
		    let diffPairName;
		    let currentIsPositive = false;
		    let diffPairComplementName;
		    if (netName?.toLowerCase().endsWith('_p') || netName?.toLowerCase().endsWith('_n')) {
		        diffPairName = netName.slice(0, -2);
		        currentIsPositive = netName.toLowerCase().endsWith('_p');
		        if (currentIsPositive) {
		            diffPairComplementName = diffPairName + '_N';
		        }
		        else {
		            diffPairComplementName = diffPairName + '_P';
		        }
		    }
		    else if (netName.endsWith('+') || netName.endsWith('-')) {
		        diffPairName = netName.slice(0, -1);
		        currentIsPositive = netName.endsWith('+');
		        if (currentIsPositive) {
		            diffPairComplementName = diffPairName + '-';
		        }
		        else {
		            diffPairComplementName = diffPairName + '+';
		        }
		    }
		    return { diffPairName, currentIsPositive, diffPairComplementName };
		}
		function setLayerSelector(selector) {
		    layerSelector = selector;
		}
		function preParseSchematicSheet(schId, schContents, parser, warnings, projectName) {
		    const mainSheet = {
		        numberingOrder: Number.NEGATIVE_INFINITY,
		        uuid: schId,
		        name: schId,
		        filename: schId,
		        contents: schContents,
		        netPath: '/',
		        projectName,
		    };
		    const sheets = [mainSheet];
		    const tree = parser.parseFile(schId, schContents);
		    let maxX = 1;
		    let minX = 0;
		    let sheetSymbolIndex = 0;
		    const sheetUuidOccurrences = new Map();
		    for (const node of tree.nodes) {
		        switch (node.name) {
		            case 'uuid': {
		                if (typeof node.values[0] === 'string') {
		                    mainSheet.uuid = (0, Util_1.removeQuotes)(node.values[0]);
		                }
		                break;
		            }
		            case 'sheet': {
		                sheetSymbolIndex += 1;
		                const sheetInfo = (0, v6ExtractSheetInfo_1.extractSheetInfo)(node);
		                if (!sheetInfo.uuid || !sheetInfo.sheetfile || !sheetInfo.sheetname) {
		                    continue;
		                }
		                const sourceSymbolOccurrence = (sheetUuidOccurrences.get(sheetInfo.uuid) ?? 0) + 1;
		                sheetUuidOccurrences.set(sheetInfo.uuid, sourceSymbolOccurrence);
		                let instances;
		                for (const n2 of node.nodes) {
		                    if (n2.name === 'instances') {
		                        instances = extractSymbolInstances(n2, warnings, projectName);
		                    }
		                }
		                maxX = Math.max(maxX, sheetInfo.start[0] + sheetInfo.size[0]);
		                minX = Math.min(minX, sheetInfo.start[0]);
		                const parentInstances = instances?.instances.length ? instances.instances : [undefined];
		                const altiumRepeatMatch = sheetInfo.sheetname.match(/Repeat\(([^,]+),(\d+),(\d+)\)/i);
		                if (altiumRepeatMatch) {
		                    const channelIdentifier = altiumRepeatMatch[1];
		                    const channelIndex1 = parseInt(altiumRepeatMatch[2]);
		                    const lastChannelIndexN = parseInt(altiumRepeatMatch[3]);
		                    if (typeof channelIndex1 !== 'number' ||
		                        typeof lastChannelIndexN !== 'number' ||
		                        Number.isNaN(channelIndex1) ||
		                        Number.isNaN(lastChannelIndexN)) {
		                        warnings.push(`Invalid channel index in sheet name ${sheetInfo.sheetname} - ${altiumRepeatMatch[2]} or ${altiumRepeatMatch[3]} is not a number`);
		                        continue;
		                    }
		                    if (channelIndex1 < 0 || lastChannelIndexN < 0) {
		                        warnings.push(`Invalid channel index in sheet name ${sheetInfo.sheetname} - ${altiumRepeatMatch[2]} or ${altiumRepeatMatch[3]} is negative`);
		                        continue;
		                    }
		                    if (channelIndex1 > lastChannelIndexN) {
		                        warnings.push(`Invalid channel index in sheet name ${sheetInfo.sheetname} - ${altiumRepeatMatch[2]} is greater than ${altiumRepeatMatch[3]}`);
		                        continue;
		                    }
		                    for (const parentInstance of parentInstances) {
		                        for (let i = channelIndex1; i <= lastChannelIndexN; ++i) {
		                            const childUuid = i === channelIndex1 ? sheetInfo.uuid : `${sheetInfo.uuid}-${i}`;
		                            const parentInstancePath = parentInstance ? (0, project_1.joinKicadInstancePath)(parentInstance.path, '') : undefined;
		                            const annotationPath = parentInstancePath ? (0, project_1.joinKicadInstancePath)(parentInstancePath, childUuid) : undefined;
		                            sheets.push({
		                                numberingOrder: i,
		                                uuid: childUuid,
		                                filename: simplePathResolve(sheetInfo.sheetfile, mainSheet.filename),
		                                name: `${channelIdentifier}${i}`,
		                                parentInstancePath,
		                                instancePath: annotationPath,
		                                annotationPath,
		                                instanceProjectName: parentInstance?.projectName,
		                                sourceSymbolKey: `${sheetSymbolIndex}:${childUuid}`,
		                                sourceSymbolOccurrence,
		                                sourceSheetUuid: childUuid,
		                                page: parentInstance?.page,
		                                netPath: '/',
		                                render: {
		                                    start: sheetInfo.start,
		                                    size: sheetInfo.size,
		                                },
		                                metadata: {
		                                    'altium-RoomName': `${channelIdentifier}${i}`,
		                                    'altium-ChannelPrefix': channelIdentifier,
		                                    'altium-ChannelIndex': i,
		                                },
		                            });
		                        }
		                    }
		                }
		                else {
		                    for (const parentInstance of parentInstances) {
		                        const parentInstancePath = parentInstance ? (0, project_1.joinKicadInstancePath)(parentInstance.path, '') : undefined;
		                        const annotationPath = parentInstancePath ? (0, project_1.joinKicadInstancePath)(parentInstancePath, sheetInfo.uuid) : undefined;
		                        sheets.push({
		                            numberingOrder: 0,
		                            uuid: sheetInfo.uuid,
		                            filename: simplePathResolve(sheetInfo.sheetfile, mainSheet.filename),
		                            name: sheetInfo.sheetname,
		                            parentInstancePath,
		                            instancePath: annotationPath,
		                            annotationPath,
		                            instanceProjectName: parentInstance?.projectName,
		                            sourceSymbolKey: `${sheetSymbolIndex}:${sheetInfo.uuid}`,
		                            sourceSymbolOccurrence,
		                            sourceSheetUuid: sheetInfo.uuid,
		                            page: parentInstance?.page,
		                            netPath: '/',
		                            render: {
		                                start: sheetInfo.start,
		                                size: sheetInfo.size,
		                            },
		                        });
		                    }
		                }
		                break;
		            }
		            case 'sheet_instances': {
		                mainSheet.legacySheetInstances = extractLegacySheetInstances(node);
		                mainSheet.page = mainSheet.legacySheetInstances.find((instance) => instance.path === '/')?.page;
		                break;
		            }
		            case 'symbol_instances': {
		                mainSheet.legacySymbolInstances = extractLegacySymbolInstances(node);
		                break;
		            }
		        }
		    }
		    for (const sheet of sheets) {
		        if (!sheet.render) {
		            continue;
		        }
		        sheet.numberingOrder =
		            (sheet.render.start[0] + sheet.render.start[1] * (maxX - minX + 1)) * 1000 + sheet.numberingOrder;
		    }
		    sheets.sort((a, b) => a.numberingOrder - b.numberingOrder);
		    for (let i = 0; i < sheets.length; ++i) {
		        sheets[i].numberingOrder = i + 1;
		    }
		    return sheets;
		}
		function simplePathResolve(path, base) {
		    if (path.startsWith('/')) {
		        return path;
		    }
		    const parts = base.split('/');
		    parts.pop();
		    parts.push(path);
		    for (let i = 0; i < parts.length; ++i) {
		        if (parts[i] === '..' && i > 0) {
		            parts.splice(i - 1, 2);
		            i -= 2;
		        }
		    }
		    return parts.join('/');
		}
		function getModuleUnitNumberSuffix(unitNo) {
		    return (0, KicadNetNamingStrategy_1.kicadUnitSubReference)(unitNo);
		}
		function shouldStoreKicadSourceReference(libraryUnitCount, isMultiUnit, moduleName, sourceReference) {
		    return (libraryUnitCount > 1 &&
		        !isMultiUnit &&
		        sourceReference.length > 0 &&
		        sourceReference !== moduleName &&
		        !sourceReference.endsWith('?') &&
		        !sourceReference.startsWith('#'));
		}
		function isAnnotatedKicadModuleName(moduleName) {
		    return moduleName.startsWith('#');
		}
		function getShouldAnnotateModuleName(moduleName) {
		    if (!moduleName.startsWith('#')) {
		        return false;
		    }
		    if (moduleName.endsWith('?')) {
		        return true;
		    }
		    return !moduleName.match(/\d+$/);
		}
		function selectPlacedUnitSymbol(symbols, unitNo, bodyStyle, unitStr) {
		    const byBodyStyleThenName = (left, right) => {
		        const bodyStyleOrder = (left.metadata?.bodyStyle ?? 0) - (right.metadata?.bodyStyle ?? 0);
		        return bodyStyleOrder !== 0 ? bodyStyleOrder : left.name.localeCompare(right.name);
		    };
		    const placedUnitSymbols = typeof unitNo === 'number' ?
		        symbols.filter((symbol) => symbol.metadata?.unit === unitNo).sort(byBodyStyleThenName)
		        : [];
		    const unitZeroSymbols = typeof unitNo === 'number' ?
		        symbols.filter((symbol) => symbol.metadata?.unit === 0 && symbol.pins.length > 0).sort(byBodyStyleThenName)
		        : [];
		    return (placedUnitSymbols.find((symbol) => symbol.metadata?.bodyStyle === bodyStyle) ??
		        placedUnitSymbols.find((symbol) => symbol.pins.length > 0) ??
		        unitZeroSymbols.find((symbol) => symbol.metadata?.bodyStyle === 1) ??
		        unitZeroSymbols[0] ??
		        placedUnitSymbols[0] ??
		        symbols.find((symbol) => symbol.name.endsWith(unitStr)));
		}
		function getDefaultPinName(pin) {
		    return pin.name === '~' || pin.name === '' || pin.name === pin.number ? pin.number : pin.name;
		}
		function parseModule(project, node, warnings, model, netCalc, resolver, opts = {}) {
		    let componentName = opts.defaultComponentName || '';
		    let componentNameOverride;
		    let moduleName = opts.moduleName || '';
		    let sourceReference = opts.moduleName || '';
		    let moduleRef = opts.moduleRef || '';
		    let pos = [0, 0];
		    let mirrored = [false, false];
		    let angle = 0;
		    let inBom = !opts.hideInBom;
		    let dnp = Boolean(opts.isDnp);
		    let unitNo;
		    let bodyStyle = 1;
		    let unitStr = '';
		    let instances;
		    const drawProperties = { ...opts.drawProperties };
		    let value;
		    const metadata = {};
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'lib_id': {
		                componentName = (0, Util_1.removeQuotes)(n.values[0]);
		                break;
		            }
		            case 'lib_name': {
		                componentNameOverride = (0, Util_1.removeQuotes)(n.values[0]);
		                break;
		            }
		            case 'at': {
		                pos = [n.values[0], n.values[1]].map((v) => parseFloat(v));
		                if (typeof n.values[2] !== 'undefined') {
		                    angle = (0, Transform_1.clampRotation)(parseFloat(n.values[2]));
		                }
		                break;
		            }
		            case 'pin': {
		                break;
		            }
		            case 'in_bom': {
		                inBom = n.values[0] === 'yes';
		                break;
		            }
		            case 'dnp': {
		                dnp = n.values[0] === 'yes';
		                break;
		            }
		            case 'mirror': {
		                switch (n.values[0]) {
		                    case 'y':
		                        mirrored = [false, true];
		                        break;
		                    case 'x':
		                        mirrored = [true, false];
		                        break;
		                    default:
		                        warnings.push(`Mirror value of ${n.values[0]} is not implemented.`);
		                        break;
		                }
		                break;
		            }
		            case 'uuid': {
		                if (n.values.length > 0) {
		                    moduleRef = (0, Util_1.removeQuotes)(n.values[0]);
		                }
		                break;
		            }
		            case 'unit': {
		                if (n.values.length === 1) {
		                    const parsed = parseInt(n.values[0]);
		                    if (Number.isFinite(parsed) && parsed > 0) {
		                        unitNo = parsed;
		                    }
		                    else {
		                        warnings.push(`Invalid module unit number: ${parsed}`);
		                    }
		                }
		                break;
		            }
		            case 'convert':
		            case 'body_style': {
		                if (n.values.length === 1) {
		                    const parsed = parseInt(n.values[0]);
		                    if (Number.isFinite(parsed) && parsed >= 0) {
		                        bodyStyle = parsed;
		                    }
		                    else {
		                        warnings.push(`Invalid module body style number: ${parsed}`);
		                    }
		                }
		                break;
		            }
		            case 'instances': {
		                instances = extractSymbolInstances(n, project.warnings, opts.projectName);
		                break;
		            }
		        }
		    });
		    const annotationPath = opts.annotationPath ?? opts.instancePath;
		    const instanceInfo = annotationPath ?
		        instances?.instances.find((instance) => instance.path === annotationPath)
		        : instances?.instances.find((instance) => instance.finalNode === model.designId);
		    const legacyInstanceInfo = legacySymbolInstanceForPath(opts.legacySymbolInstances, annotationPath, moduleRef);
		    const resolvedInstanceInfo = instanceInfo ?? legacyInstanceInfo;
		    const missingRepeatedLegacyAnnotation = Boolean(opts.sourceInstanceCount &&
		        opts.sourceInstanceCount > 1 &&
		        !opts.isSheetSymbol &&
		        !instances &&
		        !instanceInfo &&
		        !legacyInstanceInfo);
		    if (missingRepeatedLegacyAnnotation) {
		        warnings.push(`KiCad 6 annotation missing for reused sheet ${model.sourceFile} at ${opts.instancePath ?? '/'}`);
		    }
		    if (typeof resolvedInstanceInfo?.unit === 'number') {
		        unitNo = resolvedInstanceInfo.unit;
		    }
		    let useComponentNameOverride = componentNameOverride ?? componentName;
		    if (resolver.removeAltiumSuffix) {
		        useComponentNameOverride = resolver.removeAltiumSuffix(useComponentNameOverride, project);
		    }
		    const component = project.components[useComponentNameOverride];
		    const hasExplicitUnitSymbols = component?.symbols.some((componentSymbol) => typeof componentSymbol.metadata?.unit === 'number');
		    if (hasExplicitUnitSymbols) {
		        unitNo ??= 1;
		    }
		    if (typeof unitNo === 'number') {
		        unitStr = `_${unitNo}_${bodyStyle}`;
		    }
		    const symbolName = `${useComponentNameOverride}${unitStr}`;
		    let moduleValue;
		    let setDrawingValueToValue = false;
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'property': {
		                const text = parseText(n);
		                const propMetaValue = (0, Util_1.cleanMetadataVal)(text.drawing.value);
		                if (text.drawing.ref && value) {
		                    metadata[text.drawing.ref] = propMetaValue;
		                }
		                let layer;
		                if (text.drawing.ref === 'Reference') {
		                    const foundRef = resolvedInstanceInfo?.reference;
		                    if (foundRef) {
		                        text.drawing.value = foundRef;
		                    }
		                    else if (missingRepeatedLegacyAnnotation) {
		                        text.drawing.value = kicadReferencePlaceholder(text.drawing.value);
		                    }
		                    sourceReference = text.drawing.value;
		                    if (moduleNames?.has(text.drawing.value) && typeof unitNo === 'number') {
		                        const updateModule = model.modules.find((m) => m.fullModuleName === text.drawing.value);
		                        text.drawing.value += getModuleUnitNumberSuffix(unitNo);
		                        if (updateModule &&
		                            updateModule.metadata &&
		                            updateModule.name === updateModule.fullModuleName &&
		                            typeof updateModule.metadata[constants_1.META_KICAD_UNIT] === 'number') {
		                            updateModule.name += getModuleUnitNumberSuffix(updateModule.metadata[constants_1.META_KICAD_UNIT]);
		                        }
		                        const updateText = updateModule?.drawProperties[text.drawing.ref];
		                        if (updateText) {
		                            updateText.value = updateModule.name;
		                        }
		                    }
		                    moduleName = text.drawing.value;
		                    layer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.NameLabel).id;
		                    if (moduleName.startsWith('#PWR')) {
		                        setDrawingValueToValue = true;
		                    }
		                }
		                else if (text.drawing.ref === 'Value') {
		                    layer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.ValueLabel).id;
		                    value = text.drawing.value;
		                    moduleValue = value;
		                }
		                else {
		                    layer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).id;
		                    const useValue = (0, Util_1.cleanMetadataVal)(text.drawing.value.trim());
		                    if (text.drawing.ref && useValue && useValue !== '~') {
		                        metadata[text.drawing.ref] = useValue;
		                    }
		                }
		                if (angle === 90 || angle === 180) {
		                    text.drawing.justify[0] *= -1;
		                }
		                const drawAngle = (0, Transform_1.roundToZero)((0, Transform_1.clampRotation)((text.drawing.angle || 0) - angle), 2);
		                drawProperties[text.drawing.ref] = {
		                    value: text.drawing.value,
		                    angle: drawAngle,
		                    pos: text.drawing.pos,
		                    justify: text.drawing.justify,
		                    hidden: text.hidden,
		                    layer,
		                };
		                drawProperties[text.drawing.ref].mirror = (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(drawAngle);
		                break;
		            }
		        }
		    });
		    const concreteModulePath = opts.instancePath ?
		        (0, project_1.kicadInstancePathWithoutRoot)((0, project_1.joinKicadInstancePath)(opts.instancePath, moduleRef))
		        : (0, project_1.joinKicadInstancePath)('', moduleRef);
		    const m = {
		        name: moduleName,
		        ref: resolver.validateSourceModuleRef(model.designId, concreteModulePath),
		        footprintRef: symbolName,
		        componentRef: component?.name || useComponentNameOverride,
		        layer: 'F',
		        center: pos,
		        transform: {
		            angle,
		            mirrored,
		        },
		        drawProperties,
		        pinNets: {},
		    };
		    if (unitNo !== undefined && component?.isMultiUnit) {
		        if (isAnnotatedKicadModuleName(m.name)) {
		            project.warnings.push(`Module ${m.name} is an annotated name, but is also a multi-unit component. This may not work correctly.`);
		        }
		        const unitSuffix = (0, KicadNetNamingStrategy_1.kicadUnitSubReference)(unitNo);
		        if (m.name.endsWith(unitSuffix)) {
		            m.fullModuleName = m.name.substring(0, m.name.length - unitSuffix.length);
		        }
		        else {
		            m.fullModuleName = m.name;
		            m.name = m.name + unitSuffix;
		        }
		    }
		    const bareReference = sourceReference;
		    const libraryUnitCount = Math.max(1, ...(component?.symbols ?? []).map((symbol) => typeof symbol.metadata?.unit === 'number' && symbol.metadata.unit > 0 ? symbol.metadata.unit : 0));
		    const pinUnitReference = libraryUnitCount > 1 && unitNo ? `${bareReference}${(0, KicadNetNamingStrategy_1.kicadUnitSubReference)(unitNo)}` : bareReference;
		    if (getShouldAnnotateModuleName(moduleName)) {
		        const originalName = moduleName;
		        let matchModuleName = moduleName;
		        if (moduleName.endsWith('?')) {
		            matchModuleName = moduleName.substring(0, moduleName.length - 1);
		        }
		        moduleName = resolver.getNextSequentialName(model.designId, matchModuleName);
		        m.name = moduleName;
		        if (drawProperties && Object.prototype.hasOwnProperty.call(drawProperties, 'Reference')) {
		            m.drawProperties['Reference'].value = moduleName;
		            m.drawProperties['Reference'].metadata = {
		                ...(m.drawProperties['Reference'].metadata ?? {}),
		                originalName,
		            };
		        }
		    }
		    else if (isAnnotatedKicadModuleName(moduleName)) {
		        moduleName = resolver.validateAnnotatedName(model.designId, moduleName);
		        m.name = moduleName;
		    }
		    else {
		        moduleName = resolver.validateSourceModuleName(model.designId, moduleName, m.ref);
		        m.name = moduleName;
		    }
		    if (setDrawingValueToValue && value) {
		        if (drawProperties && Object.prototype.hasOwnProperty.call(drawProperties, 'Value')) {
		            m.drawProperties['Value'].value = value;
		        }
		    }
		    if (m.ref.length === 0) {
		        warnings.push(`Module ${m.name} did not have an id`);
		    }
		    const shouldStoreSourceReference = shouldStoreKicadSourceReference(libraryUnitCount, Boolean(component?.isMultiUnit), m.name, sourceReference);
		    if (typeof unitNo === 'number' || shouldStoreSourceReference) {
		        m.metadata = {
		            ...(typeof unitNo === 'number' ? { [constants_1.META_KICAD_UNIT]: unitNo } : {}),
		            ...(shouldStoreSourceReference ? { 'kicad.sourceReference': sourceReference } : {}),
		        };
		    }
		    if (component) {
		        let symbol = selectPlacedUnitSymbol(component.symbols, unitNo, bodyStyle, unitStr);
		        if (!symbol && unitNo && component.symbols.length >= unitNo) {
		            symbol = component.symbols[unitNo - 1];
		            if (symbol && symbol.metadata?.unit !== 0 && symbol.metadata?.unit !== unitNo) {
		                warnings.push(`Could not find expected symbol with name '${symbolName}', using '${symbol.name}' instead`);
		            }
		        }
		        if (symbol) {
		            m.footprintRef = symbol.name;
		            const placedPins = hasExplicitUnitSymbols ?
		                [
		                    ...new Set(component.symbols
		                        .filter((s) => (s.metadata?.unit === 0 || s.metadata?.unit === unitNo) &&
		                        (s.metadata?.bodyStyle === 0 || s.metadata?.bodyStyle === bodyStyle))
		                        .flatMap((s) => s.pins)),
		                ]
		                : symbol.pins;
		            const pinNumbersByName = new Map();
		            const pinCountByNumberAndDefaultName = new Map();
		            const pinOccurrenceByNumberAndDefaultName = new Map();
		            for (const pin of placedPins) {
		                if (pin.type === 'pin' && pin.metadata?.[KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY] !== 'no_connect') {
		                    const numbers = pinNumbersByName.get(pin.name) ?? new Set();
		                    numbers.add(pin.number);
		                    pinNumbersByName.set(pin.name, numbers);
		                    const defaultName = getDefaultPinName(pin);
		                    const countsByDefaultName = pinCountByNumberAndDefaultName.get(pin.number) ?? new Map();
		                    countsByDefaultName.set(defaultName, (countsByDefaultName.get(defaultName) ?? 0) + 1);
		                    pinCountByNumberAndDefaultName.set(pin.number, countsByDefaultName);
		                }
		            }
		            for (const drawing of placedPins) {
		                if (drawing.type !== 'pin') {
		                    continue;
		                }
		                const netPosition = (0, Transform_1.translatePoint)(pos, (0, Transform_1.getXyWithMirror)((0, Transform_1.rotatePoint)(drawing.start, -angle), mirrored));
		                const connection = {
		                    moduleRef: m.ref,
		                    pinRef: drawing.ref.toString(),
		                    moduleName: m.name,
		                };
		                if (drawing.isNetLabel) {
		                    const labelValue = KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(value ?? drawing.name);
		                    opts.globalDriverNames?.add(labelValue);
		                    connection.appliesNetName = { label: labelValue, labelType: 'power_pin' };
		                }
		                else {
		                    const electricalTypeFromMeta = drawing.metadata?.[KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY];
		                    const pinInfo = {
		                        refDes: bareReference,
		                        unitRefDes: pinUnitReference,
		                        pinName: drawing.name,
		                        pinNumber: drawing.number,
		                        isAnnotated: !bareReference.endsWith('?'),
		                        symbolUuid: moduleRef,
		                        electricalType: typeof electricalTypeFromMeta === 'string' ?
		                            electricalTypeFromMeta
		                            : undefined,
		                        hasMultiplePinsWithSameName: [...(pinNumbersByName.get(drawing.name) ?? [])].some((n) => n !== drawing.number),
		                    };
		                    const defaultPinName = getDefaultPinName(drawing);
		                    const duplicatePinCount = pinCountByNumberAndDefaultName.get(drawing.number)?.get(defaultPinName) ?? 0;
		                    const occurrencesByDefaultName = pinOccurrenceByNumberAndDefaultName.get(drawing.number) ?? new Map();
		                    const duplicateIndex = occurrencesByDefaultName.get(defaultPinName) ?? 0;
		                    occurrencesByDefaultName.set(defaultPinName, duplicateIndex + 1);
		                    pinOccurrenceByNumberAndDefaultName.set(drawing.number, occurrencesByDefaultName);
		                    const label = opts.isSheetSymbol ? netCalc.namingStrategy.getPinNetName(pinInfo)
		                        : duplicatePinCount > 1 ? netCalc.namingStrategy.nameSymbolPinHint(pinInfo, duplicateIndex)
		                            : netCalc.namingStrategy.nameSymbolPin(pinInfo);
		                    if (label) {
		                        connection.appliesNetName = {
		                            label,
		                            labelType: opts.isSheetSymbol ? 'sheet_pin'
		                                : duplicatePinCount === 1 ? 'pin'
		                                    : 'pin_hint',
		                        };
		                    }
		                }
		                netCalc.addConnection(netPosition, connection);
		            }
		        }
		        else {
		            warnings.push(`Component ${useComponentNameOverride} did not have symbol ${symbolName}`);
		        }
		    }
		    else if (!opts.allowMissingComponent) {
		        warnings.push(`Could not find component with name ${useComponentNameOverride}`);
		    }
		    let metaModule = project.modules.find((m2) => m2.name === (m.fullModuleName ?? m.name));
		    if (!metaModule) {
		        metaModule = {
		            name: m.fullModuleName ?? m.name,
		            instances: [],
		            componentRef: m.componentRef,
		            value: moduleValue,
		        };
		        project.modules.push(metaModule);
		    }
		    if (!metaModule.name) {
		        metaModule.name = m.fullModuleName ?? m.name;
		    }
		    if (!metaModule.value) {
		        metaModule.value = moduleValue;
		    }
		    const { ['kicad.sourceReference']: _sourceReference, ...moduleMetadata } = metadata;
		    if (Object.entries(moduleMetadata).length > 0) {
		        metaModule.metadata = {
		            ...(metaModule.metadata ?? {}),
		            ...moduleMetadata,
		        };
		    }
		    const drawPropertyValue = m.drawProperties['Value'];
		    if (drawPropertyValue) {
		        drawPropertyValue.value = (0, Util_1.getValueFromMetadata)(drawPropertyValue.value, metaModule);
		    }
		    if (metaModule.value) {
		        metaModule.value = (0, Util_1.getValueFromMetadata)(metaModule.value, metaModule);
		    }
		    metaModule.instances.push({
		        ref: m.ref,
		        designId: model.designId,
		    });
		    if (!inBom) {
		        if (metaModule.hideInBom === false) {
		            warnings.push(`Module ${m.name} marked as not in BOM, but instance '${m.ref}' is`);
		        }
		        metaModule.hideInBom = true;
		    }
		    if (useComponentNameOverride.includes('NetTie')) {
		        metaModule.isDnp = true;
		        dnp = true;
		    }
		    if (dnp) {
		        if (metaModule.isDnp === false) {
		            warnings.push(`Module ${m.name} marked as DNP, but instance '${m.ref}' is not`);
		        }
		        metaModule.isDnp = true;
		    }
		    if (value) {
		        value = (0, Util_1.getValueFromMetadata)(value, metaModule);
		        if (metaModule.value && metaModule.value !== value) {
		            if (!component?.isMultiUnit) {
		                warnings.push(`Module ${m.name} has different values '${metaModule.value}' and '${value}'`);
		                metaModule.value = value;
		            }
		        }
		        else {
		            metaModule.value = value;
		        }
		    }
		    moduleNames?.add(m.name);
		    return m;
		}
		function parseNoConnectPosition(node) {
		    const at = node.nodes.find((n) => n.name === 'at');
		    if (!at) {
		        return undefined;
		    }
		    const x = parseFloat(at.values[0]);
		    const y = parseFloat(at.values[1]);
		    return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : undefined;
		}
		function parseJunction(node) {
		    let pos = [];
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'at': {
		                pos = [n.values[0], n.values[1]].map((v) => parseFloat(v));
		            }
		        }
		    });
		    return {
		        type: 'junction',
		        position: pos,
		    };
		}
		function parseText(node) {
		    let hidden = (0, nodeIsHidden_1.nodeIsHidden)(node);
		    let angle = 0;
		    let fontSize = 0;
		    let thickness = 0;
		    let pos = [0, 0];
		    let italic = false;
		    const justify = [0, 0];
		    const [ref, value] = node.values.map((v) => (0, Util_1.removeQuotes)(v));
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'at': {
		                pos = [parseFloat(n.values[0]), parseFloat(n.values[1])];
		                if (n.values.length >= 2) {
		                    angle = parseFloat(n.values[2]);
		                }
		                break;
		            }
		            case 'effects': {
		                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
		                    hidden = true;
		                }
		                const fontNode = n.nodes.find((n) => n.name === 'font');
		                if (fontNode) {
		                    const sizeNode = fontNode.nodes.find((n) => n.name === 'size');
		                    if (sizeNode) {
		                        fontSize = parseFloat(sizeNode.values[0]);
		                    }
		                    const thicknessNode = fontNode.nodes.find((n) => n.name === 'thickness');
		                    if (thicknessNode) {
		                        thickness = parseFloat(thicknessNode.values[0]);
		                    }
		                    italic = fontNode.values.includes('italic');
		                }
		                const justifyNode = n.nodes.find((n) => n.name === 'justify');
		                if (justifyNode) {
		                    if (justifyNode.values.includes('left')) {
		                        justify[0] = -1;
		                    }
		                    if (justifyNode.values.includes('right')) {
		                        justify[0] = 1;
		                    }
		                    if (justifyNode.values.includes('top')) {
		                        justify[1] = -1;
		                    }
		                    if (justifyNode.values.includes('bottom')) {
		                        justify[1] = 1;
		                    }
		                }
		                break;
		            }
		        }
		    });
		    return {
		        hidden,
		        drawing: (0, parseTextTransforms_1.parseTextTransforms)({
		            type: 'text',
		            ref,
		            value,
		            size: fontSize,
		            pos: pos,
		            thickness,
		            angle,
		            justify,
		            attr: italic ? ['italic'] : [],
		        }),
		    };
		}
		function parseCircle(node, isModule) {
		    let pos = [0, 0];
		    let radius = 0;
		    let filled = 0;
		    let strokeWidth = 0;
		    const flipY = isModule ? -1 : 1;
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'center': {
		                pos = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
		                break;
		            }
		            case 'radius': {
		                radius = parseFloat(n.values[0]);
		                break;
		            }
		            case 'fill': {
		                const typeNode = n.nodes.find((v) => v.name === 'type');
		                if (typeNode) {
		                    filled = typeNode.values[0] === 'none' ? 0 : 1;
		                }
		                break;
		            }
		            case 'stroke': {
		                const widthNode = n.nodes.find((v) => v.name === 'width');
		                if (widthNode) {
		                    strokeWidth = parseFloat(widthNode.values[0]);
		                }
		                break;
		            }
		        }
		    });
		    return {
		        type: 'circle',
		        center: pos,
		        radius,
		        filled: filled === 1,
		        width: strokeWidth || DEFAULT_LINE_WIDTH,
		    };
		}
		function extractSymbolInstances(node, warnings, projectName) {
		    const projectBlocks = [];
		    for (const instanceNode of node.nodes) {
		        if (instanceNode.name !== 'project') {
		            const warning = `Unknown instance node ${instanceNode.name}`;
		            if (!warnings.includes(warning)) {
		                warnings.push(warning);
		            }
		            continue;
		        }
		        const project = {
		            projectName: (0, Util_1.removeQuotes)(instanceNode.values[0]),
		            instances: [],
		        };
		        for (const projectInstance of instanceNode.nodes) {
		            if (projectInstance.name !== 'path') {
		                continue;
		            }
		            const path = (0, project_1.joinKicadInstancePath)((0, Util_1.removeQuotes)(projectInstance.values[0]), '');
		            const pathSplit = path.substring(1).split('/');
		            const instance = {
		                projectName: project.projectName,
		                path,
		                finalNode: pathSplit[pathSplit.length - 1],
		            };
		            for (const pathNode of projectInstance.nodes) {
		                switch (pathNode.name) {
		                    case 'reference':
		                        instance.reference = (0, Util_1.removeQuotes)(pathNode.values[0]);
		                        break;
		                    case 'unit':
		                        instance.unit = parseInt(pathNode.values[0]);
		                        break;
		                    case 'page':
		                        instance.page = (0, Util_1.removeQuotes)(pathNode.values[0]);
		                        break;
		                }
		            }
		            project.instances.push(instance);
		        }
		        projectBlocks.push(project);
		    }
		    const matchingProjects = projectName ? projectBlocks.filter((project) => project.projectName === projectName) : [];
		    const selectedProjects = matchingProjects.length > 0 ? matchingProjects : projectBlocks;
		    return { instances: selectedProjects.flatMap((project) => project.instances) };
		}
		function extractLegacySymbolInstances(node) {
		    const instances = [];
		    for (const pathNode of node.nodes) {
		        if (pathNode.name !== 'path') {
		            continue;
		        }
		        const instance = {
		            path: (0, project_1.joinKicadInstancePath)((0, Util_1.removeQuotes)(pathNode.values[0]), ''),
		        };
		        for (const valueNode of pathNode.nodes) {
		            if (valueNode.name === 'reference') {
		                instance.reference = (0, Util_1.removeQuotes)(valueNode.values[0]);
		            }
		            else if (valueNode.name === 'unit') {
		                instance.unit = parseInt(valueNode.values[0]);
		            }
		        }
		        instances.push(instance);
		    }
		    return instances;
		}
		function legacySymbolInstanceForPath(instances, instancePath, symbolUuid) {
		    if (!instances || !instancePath || !symbolUuid) {
		        return undefined;
		    }
		    const childPath = joinKicadInstancePathWithoutRoot(instancePath, symbolUuid);
		    return instances.find((instance) => instance.path === childPath);
		}
		function joinKicadInstancePathWithoutRoot(instancePath, symbolUuid) {
		    return (0, project_1.joinKicadInstancePath)((0, project_1.kicadInstancePathWithoutRoot)(instancePath), symbolUuid);
		}
		function kicadReferencePlaceholder(reference) {
		    const prefix = reference.match(/^[^0-9?]+/)?.[0] ?? reference;
		    return `${prefix}?`;
		}
		function extractLegacySheetInstances(node) {
		    const instances = [];
		    for (const pathNode of node.nodes) {
		        if (pathNode.name !== 'path') {
		            continue;
		        }
		        const pageNode = pathNode.nodes.find((child) => child.name === 'page');
		        instances.push({
		            path: (0, project_1.joinKicadInstancePath)((0, Util_1.removeQuotes)(pathNode.values[0]), ''),
		            page: pageNode?.values[0] !== undefined ? (0, Util_1.removeQuotes)(pageNode.values[0]) : undefined,
		        });
		    }
		    return instances;
		}
		function sheetPortSymbolPoints(shape, half) {
		    switch (shape) {
		        case 'input':
		            return {
		                angle: 180,
		                points: [
		                    [-half, half],
		                    [0, half],
		                    [half, 0],
		                    [0, -half],
		                    [-half, -half],
		                    [-half, half],
		                ],
		            };
		        case 'passive':
		            return {
		                angle: 0,
		                points: [
		                    [-half, half],
		                    [half, half],
		                    [half, -half],
		                    [-half, -half],
		                    [-half, half],
		                ],
		            };
		        case 'tri_state':
		        case 'bidirectional':
		            return {
		                angle: 0,
		                points: [
		                    [-half, 0],
		                    [0, half],
		                    [half, 0],
		                    [0, -half],
		                    [-half, 0],
		                ],
		            };
		        case 'output':
		        default:
		            return {
		                angle: 0,
		                points: [
		                    [-half, half],
		                    [0, half],
		                    [half, 0],
		                    [0, -half],
		                    [-half, -half],
		                    [-half, half],
		                ],
		            };
		    }
		}
		const PORT_LABEL_INSET = 1.349;
		const PORT_LABEL_STROKE_BEARING = 0.215;
		function parseSheetPins(sheetNode, sheetStart, sheetSize) {
		    const halfW = sheetSize[0] / 2;
		    const halfH = sheetSize[1] / 2;
		    const symbolLayer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id;
		    const nameLayer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.PinName).id;
		    const pinLayer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Pin).id;
		    const pins = [];
		    let index = 0;
		    for (const pinNode of sheetNode.nodes) {
		        if (pinNode.name !== 'pin') {
		            continue;
		        }
		        const name = (0, Util_1.removeQuotes)(pinNode.values[0] ?? '');
		        const shape = pinNode.values[1] ?? 'passive';
		        let at = [0, 0];
		        let angle = 0;
		        const justify = [0, 0];
		        for (const child of pinNode.nodes) {
		            if (child.name === 'at') {
		                at = [parseFloat(child.values[0]), parseFloat(child.values[1])];
		                if (child.values.length > 2) {
		                    angle = (0, Transform_1.clampRotation)(parseFloat(child.values[2]));
		                }
		            }
		            else if (child.name === 'effects') {
		                const justifyNode = child.nodes.find((n) => n.name === 'justify');
		                if (justifyNode) {
		                    if (justifyNode.values.includes('left')) {
		                        justify[0] = -1;
		                    }
		                    if (justifyNode.values.includes('right')) {
		                        justify[0] = 1;
		                    }
		                    if (justifyNode.values.includes('top')) {
		                        justify[1] = -1;
		                    }
		                    if (justifyNode.values.includes('bottom')) {
		                        justify[1] = 1;
		                    }
		                }
		            }
		        }
		        const ref = index.toString();
		        const connectionPoint = [at[0] - sheetStart[0], at[1] - sheetStart[1]];
		        const drawPos = [connectionPoint[0] - halfW, connectionPoint[1] - halfH];
		        const inward = angle === 180 ? [1, 0]
		            : angle === 90 ? [0, 1]
		                : angle === 270 ? [0, -1]
		                    : [-1, 0];
		        const glyphHalf = 0.5;
		        const { points, angle: shapeAngle } = sheetPortSymbolPoints(shape, glyphHalf);
		        const glyphAngle = (0, Transform_1.clampRotation)(shapeAngle - angle);
		        const glyphOrigin = [drawPos[0] + inward[0] * glyphHalf, drawPos[1] + inward[1] * glyphHalf];
		        const portSymbol = {
		            type: 'polygon',
		            width: DEFAULT_LINE_WIDTH,
		            points: points.map((p) => (0, Transform_1.translatePoint)((0, Transform_1.rotatePoint)(p, glyphAngle), glyphOrigin)),
		        };
		        let justifyX = justify[0];
		        if (justifyX === 0) {
		            justifyX = angle === 180 ? -1 : 1;
		        }
		        const isHorizontalEdge = inward[1] === 0;
		        const labelInset = isHorizontalEdge && justifyX > 0 ? PORT_LABEL_INSET + PORT_LABEL_STROKE_BEARING : PORT_LABEL_INSET;
		        const label = (0, parseTextTransforms_1.parseTextTransforms)({
		            type: 'text',
		            value: name,
		            pos: [drawPos[0] + inward[0] * labelInset, drawPos[1] + inward[1] * labelInset],
		            angle: 0,
		            justify: [justifyX, justify[1]],
		            size: 1,
		            thickness: 1,
		        });
		        pins.push({
		            type: 'pin',
		            ref,
		            name,
		            number: ref,
		            start: connectionPoint,
		            end: connectionPoint,
		            layer: pinLayer,
		            metadata: { [KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY]: shape },
		            drawings: [
		                { layers: [symbolLayer], drawing: portSymbol },
		                { layers: [nameLayer], drawing: label },
		            ],
		        });
		        index++;
		    }
		    return pins;
		}
		function parseSheet(def, project, model, sch, node, sheetSymbolIndex, warnings, netCalc, resolver) {
		    const strokeWidth = 0;
		    let { start, size, sheetname, sheetfile, uuid } = (0, v6ExtractSheetInfo_1.extractSheetInfo)(node);
		    if (!sheetfile) {
		        warnings.push(`Sheet reference did not have a Sheetfile property`);
		        if (sheetname) {
		            sheetfile = sheetname;
		        }
		        else {
		            sheetfile = '<unknown>';
		        }
		    }
		    const childInstancePath = sch.instancePath && uuid ? (0, project_1.joinKicadInstancePath)(sch.instancePath, uuid) : undefined;
		    const childFilename = simplePathResolve(sheetfile, sch.filename);
		    const sourceSymbolKey = uuid ? `${sheetSymbolIndex}:${uuid}` : undefined;
		    const occurrenceMatch = def.kicadSchematics.find((sheet) => sheet.parentInstancePath === sch.instancePath &&
		        sheet.sourceSymbolKey === sourceSymbolKey &&
		        sheet.filename === childFilename);
		    const foundSheetDesignId = occurrenceMatch ??
		        (childInstancePath ?
		            (def.kicadSchematics.find((sheet) => sheet.instancePath === childInstancePath && sheet.filename === childFilename) ?? def.kicadSchematics.find((sheet) => sheet.instancePath === childInstancePath))
		            : def.kicadSchematics.find((sheet) => sheet.sourceSheetUuid === uuid || sheet.uuid === uuid));
		    if (!foundSheetDesignId) {
		        warnings.push(`Did not find input sheet for reference: ${sheetfile}`);
		    }
		    if (!sheetname) {
		        sheetname = sheetfile;
		    }
		    const compName = `schref_${uuid || sheetfile}`;
		    if (!project.components[compName]) {
		        const symbols = parseSymbol(node, compName, warnings, netCalc.netRefCtx, undefined, {}, layerSelector, resolver);
		        if (symbols.length > 0) {
		            if (symbols.length !== 1) {
		                warnings.push(`Schematic reference symbol ${compName} yielded ${symbols.length} symbols`);
		            }
		            symbols[0].pins = parseSheetPins(node, start, size);
		            const pinNames = new Set();
		            for (const pin of symbols[0].pins) {
		                if (pinNames.has(pin.name.valueOf())) {
		                    warnings.push(`Schematic reference symbol ${compName} has duplicate pin name ${pin.name}`);
		                }
		                pinNames.add(pin.name.valueOf());
		            }
		            const strokeRect = {
		                type: 'rect',
		                start: [-size[0] / 2, -size[1] / 2],
		                end: [size[0] / 2, size[1] / 2],
		                strokeWidth: (0, Utils_1.max)(strokeWidth, 0.127),
		                filled: false,
		            };
		            symbols[0].drawings.push({
		                layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
		                drawing: strokeRect,
		            });
		            project.components[compName] = {
		                name: compName,
		                footprints: [],
		                numPads: 0,
		                numPins: symbols.reduce((p, c) => p + c.pins.length, 0),
		                symbols,
		                signals: symbols[0].pins.map((p) => ({ id: p.ref, name: p.name, pinRefs: [p.ref], padRefs: [] })),
		                isVirtual: true,
		            };
		        }
		        else {
		            warnings.push(`Schematic reference symbol ${compName} yielded no symbols`);
		        }
		    }
		    const drawModule = parseModule(project, node, warnings, model, netCalc, resolver, {
		        defaultComponentName: compName,
		        moduleName: compName,
		        hideInBom: true,
		        isDnp: true,
		        isSheetSymbol: true,
		        instancePath: sch.instancePath,
		        annotationPath: sch.annotationPath,
		        projectName: sch.projectName,
		        legacySymbolInstances: sch.legacySymbolInstances,
		        sourceInstanceCount: sch.sourceInstanceCount,
		    });
		    model.modules.push(drawModule);
		    drawModule.center = [start[0] + size[0] / 2, start[1] + size[1] / 2];
		    const schRef = {
		        type: 'schref',
		        file: sheetfile,
		        name: sheetname,
		        designId: foundSheetDesignId?.uuid ?? uuid ?? null,
		        drawModule: drawModule,
		    };
		    return schRef;
		}
		exports.KICAD_PIN_ELECTRICAL_TYPES = [
		    'input',
		    'output',
		    'bidirectional',
		    'tri_state',
		    'passive',
		    'free',
		    'unspecified',
		    'power_in',
		    'power_out',
		    'open_collector',
		    'open_emitter',
		    'no_connect',
		];
		function parsePin(symbolNode, node, isModule, defaultNumber, netRefCtx, metadata, rootOffset) {
		    let name = '';
		    let start = [0, 0];
		    let rotation = 0;
		    let length = 0;
		    let showName = true;
		    let showNumber = true;
		    let nameOffset;
		    let numberOffset;
		    let electricalType;
		    const nameText = {
		        type: 'text',
		        pos: [0, 0],
		        attr: [],
		        justify: [-1, 0],
		        size: 1,
		        thickness: 1,
		        value: '',
		    };
		    const numberText = {
		        type: 'text',
		        pos: [0, 0],
		        attr: [],
		        justify: [0, -1],
		        size: 1,
		        thickness: 1,
		        value: defaultNumber,
		    };
		    const flipY = isModule ? -1 : 1;
		    for (const v of node.values) {
		        if (exports.KICAD_PIN_ELECTRICAL_TYPES.includes(v)) {
		            electricalType = v;
		            break;
		        }
		    }
		    for (const v of node.values) {
		        switch (v) {
		            case 'hide': {
		                showName = false;
		                showNumber = false;
		                break;
		            }
		            default: {
		                if (v.startsWith('"') && v.endsWith('"')) {
		                    name = (0, Util_1.removeQuotes)(v);
		                }
		            }
		        }
		    }
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'at': {
		                start = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
		                if (n.values.length === 3) {
		                    rotation = flipY * parseFloat(n.values[2]);
		                }
		                break;
		            }
		            case 'length': {
		                length = parseFloat(n.values[0]);
		                break;
		            }
		            case 'hide': {
		                if ((0, nodeIsHidden_1.nodeIsHidden)(node)) {
		                    showName = false;
		                    showNumber = false;
		                }
		                break;
		            }
		            case 'name': {
		                name = (0, Util_1.removeQuotes)(n.values[0]);
		                nameText.value = name;
		                const fontSizeNode = n.nodes
		                    .find((n) => n.name === 'effects')
		                    ?.nodes.find((n) => n.name === 'font')
		                    ?.nodes?.find((n) => n.name === 'size');
		                if (fontSizeNode) {
		                    nameText.size = parseFloat(fontSizeNode.values[0]);
		                    nameText.thickness = parseFloat(fontSizeNode.values[0]);
		                }
		                if ((0, nodeIsHidden_1.nodeIsHidden)(n) || nameText.value === '~') {
		                    showName = false;
		                }
		                break;
		            }
		            case 'number': {
		                numberText.value = (0, Util_1.removeQuotes)(n.values[0]);
		                const fontSizeNode = n.nodes
		                    .find((n) => n.name === 'effects')
		                    ?.nodes.find((n) => n.name === 'font')
		                    ?.nodes?.find((n) => n.name === 'size');
		                if (fontSizeNode) {
		                    numberText.size = parseFloat(fontSizeNode.values[0]);
		                    numberText.thickness = parseFloat(fontSizeNode.values[0]);
		                }
		                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
		                    showNumber = false;
		                }
		                break;
		            }
		        }
		    });
		    symbolNode.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'pin_numbers': {
		                const offsetNode = n.nodes.find((n) => n.name === 'offset');
		                if (offsetNode) {
		                    numberOffset = parseFloat(offsetNode.values[0]);
		                }
		                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
		                    showNumber = false;
		                }
		                break;
		            }
		            case 'pin_names': {
		                const offsetNode = n.nodes.find((n) => n.name === 'offset');
		                if (offsetNode) {
		                    nameOffset = parseFloat(offsetNode.values[0]);
		                }
		                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
		                    showName = false;
		                }
		                break;
		            }
		        }
		    });
		    const end = (0, Transform_1.getLineOffset)(length, rotation, start);
		    nameText.pos = (0, Transform_1.translatePoint)(end, (0, Transform_1.getLineOffset)(typeof nameOffset === 'undefined' ? nameText.size / 4 : nameOffset, rotation, [0, 0]));
		    numberText.pos = (0, Transform_1.translatePoint)(end, (0, Transform_1.getLineOffset)(typeof numberOffset === 'undefined' ? -length / 2 : -numberOffset, rotation, [0, 0]));
		    numberText.angle = nameText.angle = -rotation;
		    numberText.mirror = nameText.mirror = (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(rotation);
		    if (nameText.mirror[0]) {
		        nameText.justify = [1, 0];
		        numberText.justify = [0, -1];
		    }
		    const pin = {
		        type: 'pin',
		        ref: numberText.value || name,
		        name,
		        number: numberText.value,
		        start: (0, Transform_1.translatePoint)(start, rootOffset),
		        end: (0, Transform_1.translatePoint)(end, rootOffset),
		        layer: (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Pin).id,
		        drawings: [],
		        ...(electricalType ? { metadata: { [KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY]: electricalType } } : {}),
		    };
		    const isNetLabel = node.values.includes('power_in') &&
		        symbolNode.nodes.some((n) => n.name === 'power') &&
		        metadata?.Reference === '#PWR';
		    if (isNetLabel) {
		        pin.isNetLabel = isNetLabel;
		        pin.name = sigma_engine_shared_1.NetRef.fromString(netRefCtx, metadata?.Value?.toString() ?? pin.name).strCast();
		    }
		    if (showName && nameText.value.length > 0) {
		        pin.drawings.push({
		            layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.PinName).id],
		            drawing: (0, parseTextTransforms_1.parseTextTransforms)(nameText),
		        });
		    }
		    if (showNumber && numberText.value.length > 0) {
		        pin.drawings.push({
		            layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.PinNumber).id],
		            drawing: numberText,
		        });
		    }
		    if (pin.drawings.length === 0) {
		        delete pin.drawings;
		    }
		    return pin;
		}
		function parseWire(node) {
		    let start = [0, 0];
		    let end = [0, 0];
		    let stroke;
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'pts': {
		                const pts = n.nodes.map((pt) => [pt.values[0], pt.values[1]].map((v) => parseFloat(v)));
		                start = pts[0];
		                end = pts[1];
		                break;
		            }
		            case 'stroke':
		                stroke = (0, v6Common_1.parseStrokeDirective)(n);
		                break;
		        }
		    });
		    return {
		        type: 'track',
		        start,
		        end,
		        width: stroke?.width || 0,
		    };
		}
		function parseBusEntry(node) {
		    let start = [0, 0];
		    let size = [0, 0];
		    let stroke;
		    let rotation = 0;
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'at': {
		                start = [parseFloat(n.values[0]), parseFloat(n.values[1])];
		                if (n.values.length === 3) {
		                    rotation += parseFloat(n.values[2]);
		                }
		                break;
		            }
		            case 'size': {
		                size = [parseFloat(n.values[0]), parseFloat(n.values[1])];
		                break;
		            }
		            case 'stroke': {
		                stroke = (0, v6Common_1.parseStrokeDirective)(n);
		                break;
		            }
		        }
		    });
		    return (0, Transform_1.rotateDrawing)({
		        type: 'track',
		        start,
		        end: (0, Transform_1.translatePoint)(size, start),
		        width: stroke?.width || 0,
		        metadata: {
		            'kicad.busentry': true,
		        },
		    }, rotation);
		}
		function parsePolyline(node, isModule) {
		    const { points, strokeWidth, style, fillType } = (0, v6ExtractPolygon_1.extractPolygon)(isModule, node);
		    const lastPoint = points[points.length - 1];
		    if (points[0][0] !== lastPoint[0] || points[0][1] !== lastPoint[1]) {
		        const segmentFlipY = isModule ? -1 : 1;
		        const segments = [];
		        for (let i = 0; i < points.length - 1; ++i) {
		            segments.push({
		                type: 'segment',
		                start: [points[i][0], segmentFlipY * points[i][1]],
		                end: [points[i + 1][0], segmentFlipY * points[i + 1][1]],
		                width: strokeWidth || DEFAULT_LINE_WIDTH,
		                style,
		            });
		        }
		        return segments;
		    }
		    return [
		        {
		            type: 'polygon',
		            filled: fillType !== 'none',
		            width: strokeWidth || DEFAULT_LINE_WIDTH,
		            points,
		            lineStyle: style,
		        },
		    ];
		}
		function parseHierarchicalLabel(project, node, netCalc, warnings, resolver) {
		    const value = (0, Util_1.removeQuotes)(node.values[0]);
		    const { pos, direction, justify, fontSize, shape } = (0, v6ParseLabel_1.extractLabelInfo)(node, false, warnings);
		    let portDirection;
		    const symbolWidth = 1;
		    const halfSymWidth = symbolWidth / 2;
		    const symbolDrawing = {
		        type: 'polygon',
		        width: 0,
		        points: [],
		        pos: [halfSymWidth, 0],
		    };
		    switch (shape) {
		        case 'input':
		            symbolDrawing.angle = 180;
		            portDirection = sigma_engine_shared_2.DesignModels.SignalDirection.Input;
		            symbolDrawing.points = [
		                [-halfSymWidth, halfSymWidth],
		                [0, halfSymWidth],
		                [halfSymWidth, 0],
		                [0, -halfSymWidth],
		                [-halfSymWidth, -halfSymWidth],
		                [-halfSymWidth, halfSymWidth],
		            ];
		            break;
		        case 'output':
		            portDirection = sigma_engine_shared_2.DesignModels.SignalDirection.Output;
		            symbolDrawing.points = [
		                [-halfSymWidth, halfSymWidth],
		                [0, halfSymWidth],
		                [halfSymWidth, 0],
		                [0, -halfSymWidth],
		                [-halfSymWidth, -halfSymWidth],
		                [-halfSymWidth, halfSymWidth],
		            ];
		            break;
		        case 'passive':
		            symbolDrawing.points = [
		                [-halfSymWidth, halfSymWidth],
		                [halfSymWidth, halfSymWidth],
		                [halfSymWidth, -halfSymWidth],
		                [-halfSymWidth, -halfSymWidth],
		                [-halfSymWidth, halfSymWidth],
		            ];
		            break;
		        case 'tri_state':
		        case 'bidirectional':
		            portDirection = sigma_engine_shared_2.DesignModels.SignalDirection.BiDirectionalIO;
		            symbolDrawing.points = [
		                [-halfSymWidth, 0],
		                [0, halfSymWidth],
		                [halfSymWidth, 0],
		                [0, -halfSymWidth],
		                [-halfSymWidth, 0],
		            ];
		            break;
		        default:
		            warnings.push(`Unsupported hierarchical label shape: ${shape}`);
		            break;
		    }
		    const validatedNetName = resolver.validateSourceNetName(project.designId, value, 'hierarchical_label');
		    const valueRef = netCalc.setLabel(pos, validatedNetName, 'hier_label');
		    const textDrawing = (0, parseTextTransforms_1.parseTextTransforms)({
		        type: 'text',
		        value,
		        pos: [symbolWidth * 1.25, 0],
		        angle: 0,
		        justify: [justify[0], 0],
		        size: fontSize,
		        thickness: 1,
		        mirror: (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction),
		    });
		    project.ports[value] = {
		        name: value,
		        nets: [{ name: valueRef.strCast(), label: value }],
		        direction: portDirection,
		    };
		    return {
		        type: 'group',
		        pos,
		        angle: direction,
		        net: valueRef.strCast(),
		        drawings: [symbolDrawing, textDrawing],
		    };
		}
		function parseGlobalLabel(node, netRefCtx, warnings) {
		    let [value] = node.values;
		    value = (0, Util_1.removeQuotes)(value);
		    const SHAPES = ['input', 'output', 'bidirectional', 'neutral'];
		    let shape = 'neutral';
		    let x = -1;
		    let y = -1;
		    let fontSize = -1;
		    let thickness = -1;
		    let direction = 0;
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'shape': {
		                const shapeValue = n.values[0];
		                if (!SHAPES.includes(shapeValue)) {
		                    warnings.push(`Label '${value}' had unexpected shape value: '${shapeValue}'`);
		                }
		                shape = shapeValue;
		                break;
		            }
		            case 'at': {
		                x = parseFloat(n.values[0]);
		                y = parseFloat(n.values[1]);
		                if (n.values.length > 2) {
		                    direction = parseFloat(n.values[2]);
		                }
		                break;
		            }
		            case 'effects': {
		                const fontNode = n.nodes.filter((n) => n.name === 'font');
		                if (fontNode?.length) {
		                    const sizeNode = fontNode[0].nodes.filter((n) => n.name === 'size');
		                    if (sizeNode?.length) {
		                        fontSize = parseFloat(sizeNode[0].values[0]);
		                    }
		                    const thicknessNode = fontNode[0].nodes.filter((n) => n.name === 'thickness');
		                    if (thicknessNode?.length) {
		                        thickness = parseFloat(thicknessNode[0].values[0]);
		                    }
		                }
		                break;
		            }
		        }
		    });
		    let orientation = 'L';
		    direction = Math.abs(direction);
		    if (direction <= 45) {
		        orientation = 'L';
		    }
		    else if (direction <= 135) {
		        orientation = 'D';
		    }
		    else if (direction <= 225) {
		        orientation = 'R';
		    }
		    else {
		        orientation = 'U';
		    }
		    return {
		        type: 'label',
		        pos: [x, y],
		        value,
		        shape,
		        orientation,
		        metadata: {
		            fontSize,
		            thickness,
		        },
		        net: sigma_engine_shared_1.NetRef.fromString(netRefCtx, value).strCast(),
		    };
		}
		function parseComponents(project, node, netRefCtx, resolver) {
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'symbol': {
		                const componentDefinition = parseComponentDefinition(n, netRefCtx, project.warnings, layerSelector, resolver);
		                if (!componentDefinition) {
		                    break;
		                }
		                if (!project.components[componentDefinition.name]) {
		                    project.components[componentDefinition.name] = componentDefinition;
		                }
		                break;
		            }
		        }
		    });
		}
		function parseComponentDefinition(node, netRefCtx, warnings, layerSelector, resolver) {
		    const name = (0, Util_1.removeQuotes)(node.values[0]);
		    const metadata = { ...kicadSymbolParser_1.KicadSymbolNameParser.parseSymbolName(name) };
		    let symbols = [];
		    let signals = [];
		    node.nodes.forEach((n) => {
		        switch (n.name) {
		            case 'property': {
		                const name = (0, Util_1.removeQuotes)(n.values[0]);
		                const value = (0, Util_1.cleanMetadataVal)((0, Util_1.removeQuotes)(n.values[1]));
		                if (value && value.length > 0 && value !== '~') {
		                    metadata[name] = value;
		                }
		                break;
		            }
		            case 'symbol': {
		                const parsedSymbols = parseSymbol(n, name, warnings, netRefCtx, node, metadata, layerSelector, resolver);
		                for (const symbol of parsedSymbols) {
		                    const pinRefsMap = new Map();
		                    const pinNameMap = new Map();
		                    for (const pin of symbol.pins) {
		                        const name = pin.name.valueOf();
		                        if (!pinRefsMap.has(pin.ref)) {
		                            pinRefsMap.set(pin.ref, [pin]);
		                        }
		                        else {
		                            pinRefsMap.get(pin.ref).push(pin);
		                        }
		                        if (!pinNameMap.has(name)) {
		                            pinNameMap.set(name, new Map([
		                                [
		                                    {
		                                        start: pin.start,
		                                        end: pin.end,
		                                    },
		                                    [pin],
		                                ],
		                            ]));
		                        }
		                        else {
		                            const pinMap = pinNameMap.get(name);
		                            const existingPin = Array.from(pinMap.keys()).find((p) => {
		                                return (Math.abs(p.start[0] - pin.start[0]) < 0.0001 &&
		                                    Math.abs(p.start[1] - pin.start[1]) < 0.0001 &&
		                                    Math.abs(p.end[0] - pin.end[0]) < 0.0001 &&
		                                    Math.abs(p.end[1] - pin.end[1]) < 0.0001);
		                            });
		                            if (existingPin) {
		                                pinMap.get(existingPin).push(pin);
		                            }
		                            else {
		                                pinMap.set({
		                                    start: pin.start,
		                                    end: pin.end,
		                                }, [pin]);
		                            }
		                        }
		                    }
		                    for (const [pinName, pinPositionMaps] of pinNameMap) {
		                        if (pinPositionMaps.size === 1) {
		                            const [pins] = Array.from(pinPositionMaps.values());
		                            const pin = pins[0];
		                            if (signals.some((s) => s.pinRefs.includes(pin.ref))) {
		                                continue;
		                            }
		                            signals.push({
		                                id: pinName,
		                                name: pin.name,
		                                pinRefs: pins.map((p) => p.ref),
		                                padRefs: pins.map((p) => p.number),
		                                metadata: {
		                                    fromUnit: symbol.metadata?.unit,
		                                },
		                            });
		                        }
		                        else {
		                            for (const pin of Array.from(pinPositionMaps.values()).flat()) {
		                                if (signals.some((s) => s.pinRefs.includes(pin.ref))) {
		                                    continue;
		                                }
		                                const pinRefs = pinRefsMap.get(pin.ref);
		                                signals.push({
		                                    id: pinRefs.length > 1 ? pin.number : pin.ref,
		                                    name: pin.name.valueOf(),
		                                    pinRefs: [pin.ref],
		                                    padRefs: [pin.number],
		                                    metadata: {
		                                        fromUnit: symbol.metadata?.unit,
		                                    },
		                                });
		                            }
		                        }
		                    }
		                }
		                symbols = symbols?.concat(parsedSymbols);
		                n.nodes.forEach((n2) => {
		                    switch (n2.name) {
		                        case 'property': {
		                            if (n2.values.length === 2) {
		                                const val = (0, Util_1.removeQuotes)(n2.values[1]);
		                                if (val && val !== '~') {
		                                    metadata[(0, Util_1.removeQuotes)(n2.values[0]).toLowerCase()] = (0, Util_1.cleanMetadataVal)(val);
		                                }
		                            }
		                        }
		                    }
		                });
		                break;
		            }
		        }
		    });
		    const lowestBodyStyleByUnit = new Map();
		    for (const symbol of symbols) {
		        if (symbol.pins.length === 0) {
		            continue;
		        }
		        const current = lowestBodyStyleByUnit.get(symbol.metadata?.unit);
		        if (!current || (symbol.metadata?.bodyStyle ?? 0) < (current.metadata?.bodyStyle ?? 0)) {
		            lowestBodyStyleByUnit.set(symbol.metadata?.unit, symbol);
		        }
		    }
		    const isMultiUnit = lowestBodyStyleByUnit.size > 1;
		    const nonzeroBodyStyles = new Set(symbols.map((symbol) => symbol.metadata?.bodyStyle).filter((bodyStyle) => bodyStyle !== undefined && bodyStyle > 0));
		    const bodyStyles = nonzeroBodyStyles.size > 0 ? [...nonzeroBodyStyles] : [0];
		    const hasExplicitBodyStyles = symbols.some((symbol) => typeof symbol.metadata?.bodyStyle === 'number');
		    const numPins = hasExplicitBodyStyles ?
		        Math.max(0, ...bodyStyles.map((bodyStyle) => new Set(symbols
		            .filter((symbol) => symbol.metadata?.bodyStyle === 0 || symbol.metadata?.bodyStyle === bodyStyle)
		            .flatMap((symbol) => symbol.pins)).size))
		        : [...lowestBodyStyleByUnit.values()].reduce((total, symbol) => total + symbol.pins.length, 0);
		    symbols = symbols.map((symbol) => {
		        if (!symbol.metadata) {
		            throw new Error(`Symbol ${symbol.name} has no metadata`);
		        }
		        if (symbol.metadata?.unit === 0 && symbol.metadata?.bodyStyle === 0) {
		            return symbol;
		        }
		        const mergeWith = symbols.filter((s) => s !== symbol &&
		            (s.metadata?.bodyStyle === 0 || s.metadata?.bodyStyle === symbol.metadata?.bodyStyle) &&
		            (s.metadata?.unit === 0 || s.metadata?.unit === symbol.metadata?.unit) &&
		            s.metadata?.orientation === symbol.metadata?.orientation);
		        return mergeWith.reduce((acc, curr) => mergeSymbols(curr, acc), symbol);
		    });
		    if (!symbols || symbols.length === 0) {
		        warnings.push(`Could not load component symbol ${name}. Creating empty symbol.`);
		        symbols.push({
		            ref: name,
		            name,
		            drawings: [],
		            pins: [],
		            metadata: { ...kicadSymbolParser_1.KicadSymbolNameParser.parseSymbolName(name) },
		        });
		    }
		    if (signals.some((s) => signals.some((s2) => s.id === s2.id && s !== s2))) {
		        const remapSignalIds = new Set();
		        for (const signal of signals) {
		            if (remapSignalIds.has(signal.id) || signals.some((s) => s.id === signal.id && s !== signal)) {
		                remapSignalIds.add(signal.id);
		                signal.id = `${signal.metadata?.fromUnit ?? signals.indexOf(signal)}${signal.id}`;
		            }
		        }
		    }
		    const isSupplySymbol = symbols.some((s) => s.pins.some((p) => p.isNetLabel === true));
		    const reference = metadata.Reference;
		    const hasVirtualReference = typeof reference === 'string' && reference.startsWith('#');
		    let component = {
		        name,
		        numPins: numPins,
		        numPads: 0,
		        footprints: [],
		        symbols,
		        signals,
		        ...(isSupplySymbol || hasVirtualReference ? { isVirtual: true } : {}),
		    };
		    if (name.includes('NetTie')) {
		        component.model = { modelId: 'jumper.2h' };
		    }
		    if (isMultiUnit) {
		        component.isMultiUnit = true;
		        for (const sym of symbols) {
		            const unitNoStr = /_([0-9]+)_([0-9]+)$/.exec(sym.name)?.[1];
		            if (!unitNoStr) {
		                continue;
		            }
		            const unitNo = unitNoStr ? parseInt(unitNoStr) : 1;
		            const blockPattern = String.fromCharCode('A'.charCodeAt(0) + unitNo - 1);
		            for (const pin of sym.pins) {
		                const pinSignal = component.signals.find((s) => s.pinRefs.includes(pin.ref));
		                pinSignal.blockInfo = {
		                    index: unitNo,
		                    blockPattern,
		                };
		            }
		        }
		    }
		    if (Object.keys(metadata).length > 0) {
		        component.metadata = metadata;
		    }
		    if (resolver?.updateAltiumSupplySymbol) {
		        component = resolver.updateAltiumSupplySymbol(component);
		    }
		    if (component.isVirtual) {
		        for (const signal of component.signals) {
		            signal.padRefs = [];
		        }
		    }
		    return component;
		}
		function parseSymbol(node, name, warnings, netRefCtx, parentSymbolNode = node, metadata, layerSelector, resolver) {
		    const symbols = [];
		    name = node.values[0] ? (0, Util_1.removeQuotes)(node.values[0]) : name;
		    if (resolver?.removeAltiumSuffixFromSymbol) {
		        name = resolver.removeAltiumSuffixFromSymbol(name, metadata);
		    }
		    const basePosition = [0, 0];
		    const symbol = {
		        ref: name,
		        name,
		        drawings: [],
		        pins: [],
		        metadata: { ...kicadSymbolParser_1.KicadSymbolNameParser.parseSymbolName(name) },
		    };
		    let subSymbolNum = 0;
		    node.nodes.forEach((n2) => {
		        switch (n2.name) {
		            case 'at': {
		                basePosition[0] = -parseFloat(n2.values[0]);
		                basePosition[1] = parseFloat(n2.values[1]);
		                break;
		            }
		        }
		    });
		    node.nodes.forEach((n2) => {
		        switch (n2.name) {
		            case 'rectangle': {
		                symbol.drawings.push(...(0, v6ParseRectangle_1.parseRectangle)(n2, true, warnings, DesignModels_1.SchLayerType.Symbol, layerSelector, Constants_1.SEGMENT_MIN_WIDTH));
		                break;
		            }
		            case 'pin': {
		                symbol.pins.push(parsePin(parentSymbolNode, n2, true, symbol.pins.length.toString(), netRefCtx, metadata, basePosition));
		                break;
		            }
		            case 'polyline': {
		                symbol.drawings.push(...parsePolyline(n2, true).map((drawing) => ({
		                    layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
		                    drawing,
		                })));
		                break;
		            }
		            case 'circle': {
		                symbol.drawings.push({
		                    layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
		                    drawing: parseCircle(n2, true),
		                });
		                break;
		            }
		            case 'arc': {
		                const arcDrawing = (0, v6ParseArc_1.parseArc)(true, n2, false, warnings, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).name, layerSelector, Constants_1.SEGMENT_MIN_WIDTH);
		                if (!arcDrawing?.drawing) {
		                    warnings.push(`Symbol ${name} has undefined arc drawing`);
		                }
		                if (arcDrawing?.drawing) {
		                    symbol.drawings.push({
		                        layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
		                        drawing: arcDrawing.drawing,
		                    });
		                }
		                break;
		            }
		            case 'text': {
		                symbol.drawings.push((0, v6ParseLabel_1.parseTextElement)(n2, true, warnings, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).name, layerSelector));
		                break;
		            }
		            case 'symbol': {
		                let symName;
		                ++subSymbolNum;
		                if (n2.values.length >= 1) {
		                    symName = (0, Util_1.removeQuotes)(n2.values[0]);
		                }
		                else {
		                    symName = `${name}_${subSymbolNum}_1`;
		                    warnings.push(`Expected symbol name ${symName}`);
		                }
		                symbols.push(...parseSymbol(n2, symName, warnings, netRefCtx, parentSymbolNode, metadata, layerSelector, resolver));
		                break;
		            }
		            default: {
		                warnings.push(`Symbol ${name} had unsupported drawing type ${n2.name}`);
		                break;
		            }
		        }
		    });
		    if (symbols.length > 0 && symbol.drawings.length > 0) {
		        warnings.push(`Symbol ${name} had drawings and sub-symbols`);
		    }
		    if (symbols.length === 0) {
		        symbols.push(symbol);
		    }
		    return symbols.map((s) => {
		        s.pins = ensureUniquePinRefs(s.pins);
		        return s;
		    });
		}
		function ensureUniquePinRefs(pins) {
		    const occurrencesByRef = new Map();
		    const assignedRefs = new Set();
		    const realPinNumbers = new Set(pins.map((pin) => pin.number));
		    for (const pin of pins) {
		        const occurrence = (occurrencesByRef.get(pin.ref) ?? 0) + 1;
		        occurrencesByRef.set(pin.ref, occurrence);
		        if (occurrence > 1) {
		            let suffix = occurrence;
		            let candidate = `${pin.ref}#${suffix}`;
		            while (realPinNumbers.has(candidate) || assignedRefs.has(candidate)) {
		                suffix++;
		                candidate = `${pin.ref}#${suffix}`;
		            }
		            pin.ref = candidate;
		        }
		        assignedRefs.add(pin.ref);
		    }
		    return pins;
		}
		function mergeSymbols(a, b) {
		    const c = {
		        ...b,
		        drawings: b.drawings.concat(a.drawings),
		        pins: b.pins.concat(a.pins),
		        metadata: {
		            ...(a.metadata ?? {}),
		            ...(b.metadata ?? {}),
		        },
		    };
		    return c;
		}
		function combineNets(ref, first, second) {
		    return {
		        ...first,
		        ...second,
		        name: ref.strCast(),
		        metadata: {
		            ...(first.metadata ?? {}),
		            ...(second.metadata ?? {}),
		        },
		        connections: first.connections.concat(second.connections),
		    };
		}
		
	} (v6schema));
	return v6schema;
}

var KicadComponentPadCount = {};

var hasRequiredKicadComponentPadCount;

function requireKicadComponentPadCount () {
	if (hasRequiredKicadComponentPadCount) return KicadComponentPadCount;
	hasRequiredKicadComponentPadCount = 1;
	Object.defineProperty(KicadComponentPadCount, "__esModule", { value: true });
	KicadComponentPadCount.KicadComponentPadCount = void 0;
	class KicadComponentPadCount$1 {
	    static deriveNumPads(footprints) {
	        const padCounts = footprints.map((footprint) => footprint.pads.length);
	        if (padCounts.length === 0) {
	            return 0;
	        }
	        if (padCounts.length === 1) {
	            return padCounts[0];
	        }
	        return padCounts;
	    }
	}
	KicadComponentPadCount.KicadComponentPadCount = KicadComponentPadCount$1;
	
	return KicadComponentPadCount;
}

var hasRequiredBoard$2;

function requireBoard$2 () {
	if (hasRequiredBoard$2) return board$1;
	hasRequiredBoard$2 = 1;
	Object.defineProperty(board$1, "__esModule", { value: true });
	board$1.parseBoard = parseBoard;
	board$1.initBoardLayerSelector = initBoardLayerSelector;
	board$1.setBoardLayerSelector = setBoardLayerSelector;
	board$1.readBoardLayer = readBoardLayer;
	board$1.readModule = readModule;
	board$1.readText = readText;
	board$1.parseTrack = parseTrack;
	board$1.readVia = readVia;
	board$1.readLine = readLine;
	board$1.readPad = readPad;
	board$1.readZone = readZone;
	const NodeParser_1 = requireNodeParser();
	const layers_1 = requireLayers$2();
	const Area_1 = requireArea();
	const Util_1 = requireUtil();
	const v6ParseRectangle_1 = requireV6ParseRectangle();
	const v6ParseArc_1 = requireV6ParseArc();
	const v6ParseLabel_1 = requireV6ParseLabel();
	const v6ExtractPolygon_1 = requireV6ExtractPolygon();
	const v6ParseTitleBlock_1 = requireV6ParseTitleBlock();
	const v6Common_1 = requireV6Common();
	const v6ParseCircle_1 = requireV6ParseCircle();
	const sigma_engine_shared_1 = requireDist$1();
	const v6schema_1 = requireV6schema();
	const fonts_1 = requireFonts();
	const DesignModels_1 = requireDesignModels();
	const Transform_1 = requireTransform();
	const Utils_1 = requireUtils$4();
	const nodeIsHidden_1 = requireNodeIsHidden();
	const KicadComponentPadCount_1 = requireKicadComponentPadCount();
	let layerSelector;
	function parseBoard(project, board, designId, resolver, netRefCtx) {
	    if (project.type !== 'kicad') {
	        throw new Error('Cannot parse kicad board to non-kicad project');
	    }
	    initBoardLayerSelector();
	    const nets = [];
	    const model = {
	        modelType: 'pcb',
	        designId,
	        sourceFile: designId,
	        edges_bbox: {
	            minx: Number.POSITIVE_INFINITY,
	            miny: Number.POSITIVE_INFINITY,
	            maxx: Number.NEGATIVE_INFINITY,
	            maxy: Number.NEGATIVE_INFINITY,
	        },
	        initial_zoombox: {
	            minx: Number.POSITIVE_INFINITY,
	            miny: Number.POSITIVE_INFINITY,
	            maxx: Number.NEGATIVE_INFINITY,
	            maxy: Number.NEGATIVE_INFINITY,
	        },
	        grid: v6Common_1.DefaultBrdGrid,
	        layerSet: layers_1.KICAD_BRD_LAYERSET,
	        throughHoleLayers: [],
	        edges: [],
	        drawings: {},
	        modules: [],
	        tracks: {},
	        vias: [],
	        zones: {},
	        nets: {},
	        font: {
	            default: fonts_1.Font.Newstroke,
	        },
	        metadata: {
	            title: '',
	            revision: '',
	            company: '',
	            date: '',
	        },
	        warnings: [],
	    };
	    if (Object.keys(project.components).length === 0) {
	        model.warnings.push(`Parse kicad schematics first for proper component linking`);
	    }
	    const tree = NodeParser_1.SExpressionFileParser.Parse(board);
	    function addDrawingToRoot(drawing, layer) {
	        const layerNo = typeof layer === 'number' ? layer : layer.id;
	        if (!model.drawings[layerNo]) {
	            model.drawings[layerNo] = [drawing];
	        }
	        else {
	            model.drawings[layerNo].push(drawing);
	        }
	    }
	    parseBoardSetup(tree.nodes.find((n) => n.name === 'setup')?.nodes, model.warnings);
	    if (!project.layerSets[layers_1.KICAD_BRD_LAYERSET]) {
	        project.layerSets[layers_1.KICAD_BRD_LAYERSET] = [];
	        tree.nodes.forEach((node) => {
	            switch (node.name) {
	                case 'layers': {
	                    node.nodes.forEach((n) => {
	                        const layer = readBoardLayer(n);
	                        const filtered = layerSelector.filterLayers(layer.name);
	                        if (!filtered.length) {
	                            model.warnings.push(`Could not parse unexpected layer '${layer.name}' at id '${layer.id}'.`);
	                        }
	                        else if (filtered.length > 1) {
	                            model.warnings.push(`Layer node resolved to multiple layers. This was not expected. '${layer.name}'.`);
	                        }
	                        else {
	                            let defaultVisible = true;
	                            if (typeof layer.defaultVisible === 'boolean') {
	                                defaultVisible = layer.defaultVisible;
	                            }
	                            else if (typeof filtered[0].defaultVisible === 'boolean') {
	                                defaultVisible = filtered[0].defaultVisible;
	                            }
	                            project.layerSets[layers_1.KICAD_BRD_LAYERSET].push({
	                                ...filtered[0],
	                                friendlyName: layer.friendlyName,
	                                name: layer.name,
	                                defaultVisible,
	                            });
	                        }
	                    });
	                    break;
	                }
	            }
	        });
	    }
	    const activeLayers = new Set(project.layerSets[layers_1.KICAD_BRD_LAYERSET].map((l) => l.id));
	    const modules = new Map();
	    tree.nodes.forEach((node) => {
	        switch (node.name) {
	            case 'title_block': {
	                (0, v6ParseTitleBlock_1.parseTitleBlock)(model, node);
	                break;
	            }
	            case 'host':
	            case 'version': {
	                model.metadata[`kicad.${node.name}`] = node.values.map((v) => (0, Util_1.removeQuotes)(v)).join(' ');
	                break;
	            }
	            case 'layers': {
	                break;
	            }
	            case 'footprint':
	            case 'module': {
	                const moduleDef = readModule(project, model, node, activeLayers);
	                if (!modules.has(moduleDef.module.name)) {
	                    modules.set(moduleDef.module.name, []);
	                }
	                modules.get(moduleDef.module.name).push(moduleDef);
	                break;
	            }
	            case 'segment': {
	                const track = parseTrack(node);
	                const layers = layerSelector.filterLayers(track.layer);
	                if (!layers.length) {
	                    model.warnings.push(`Could not parse unexpected layer '${track.layer}'.`);
	                    break;
	                }
	                if (layers.length > 1) {
	                    model.warnings.push(`Segment has multiple layers. This was not expected. '${track.layer}'`);
	                    break;
	                }
	                const layer = layers[0];
	                if (!model.tracks[layer.id]) {
	                    model.tracks[layer.id] = [];
	                }
	                track.track.layer = layer.id;
	                track.track.net = nets.find((n) => n.id === track.track.net)?.value ?? '';
	                model.tracks[layer.id].push(track.track);
	                break;
	            }
	            case 'via': {
	                const via = readVia(node, project.layerSets[layers_1.KICAD_BRD_LAYERSET]);
	                via.net = nets.find((n) => n.id === via.net)?.value ?? '';
	                model.vias.push(via);
	                break;
	            }
	            case 'gr_line': {
	                const line = readLine(node);
	                const layers = layerSelector.filterLayers(line.layer);
	                if (!layers.length) {
	                    model.warnings.push(`Could not parse unexpected layer '${line.layer}'.`);
	                    break;
	                }
	                if (layers.length > 1) {
	                    model.warnings.push(`Segment has multiple layers. This was not expected. '${line.layer}'`);
	                    break;
	                }
	                addDrawingToRoot(line.segment, layers[0]);
	                break;
	            }
	            case 'gr_rect':
	            case 'rectangle': {
	                const drawings = (0, v6ParseRectangle_1.parseRectangle)(node, false, model.warnings, DesignModels_1.PcbLayerType.Other, layerSelector);
	                for (const drawing of drawings) {
	                    for (const layer of drawing.layers) {
	                        addDrawingToRoot(drawing.drawing, layer);
	                    }
	                }
	                break;
	            }
	            case 'gr_circle': {
	                const obj = (0, v6ParseCircle_1.readCircle)(node);
	                const layers = layerSelector.filterLayers(obj.layer);
	                addDrawingToRoot(obj.circle, layers[0]);
	                break;
	            }
	            case 'arc':
	            case 'gr_arc': {
	                const drawing = (0, v6ParseArc_1.parseArc)(false, node, false, model.warnings, undefined, layerSelector);
	                if (drawing) {
	                    for (const layer of drawing.layers) {
	                        addDrawingToRoot(drawing.drawing, layer);
	                    }
	                }
	                break;
	            }
	            case 'gr_text': {
	                const result = (0, v6ParseLabel_1.parseTextElement)(node, false, model.warnings, undefined, layerSelector, false);
	                addDrawingToRoot(result.drawing, result.layers[0]);
	                break;
	            }
	            case 'gr_poly': {
	                const poly = readPolygon(node);
	                if (poly.layer) {
	                    addDrawingToRoot(poly.polygon, layerSelector.filterLayers(poly.layer)[0]);
	                }
	                break;
	            }
	            case 'zone': {
	                const zones = readZone(node);
	                zones.forEach((zone) => {
	                    if (!model.zones[zone.layer]) {
	                        model.zones[zone.layer] = [];
	                    }
	                    model.zones[zone.layer].push(zone);
	                });
	                break;
	            }
	            case 'net': {
	                nets.push({ id: node.values[0], value: (0, Util_1.removeQuotes)(node.values[1]) });
	                break;
	            }
	        }
	    });
	    for (let [name, namedModules] of modules) {
	        if (namedModules.length > 1 && resolver.resolutionOpts.deduplicateZeroValueComponents) {
	            const metaModule = project.modules.find((m) => m.name === name);
	            if (metaModule && !isEmptyValue(metaModule.value)) {
	                const matchedValue = namedModules.find((m) => m.value === metaModule.value);
	                if (matchedValue) {
	                    namedModules = [matchedValue];
	                }
	            }
	            const zeroValueModules = namedModules.filter((m) => isEmptyValue(m.value));
	            if (zeroValueModules.length > 1) {
	                namedModules = namedModules.filter((m) => !isEmptyValue(m.value));
	            }
	        }
	        if (namedModules.length > 1) {
	            model.warnings.push(`Module ${name} has multiple definitions that aren't deduplicated.`);
	        }
	        for (const { footprint, module, value } of namedModules) {
	            let existing = resolver.getComponentForFootprint(module, footprint, model);
	            if (existing) {
	                module.componentRef = existing.name;
	                if (!existing.footprints.find((fp) => fp.ref === footprint.ref)) {
	                    existing.footprints.push(footprint);
	                }
	            }
	            else {
	                module.componentRef = footprint.ref;
	                project.components[footprint.ref] = existing = {
	                    name: footprint.ref,
	                    footprints: [footprint],
	                    signals: [],
	                    symbols: [],
	                    numPins: footprint.pads.length,
	                    numPads: footprint.pads.length,
	                };
	            }
	            let metaModule = project.modules.find((m) => m.name === module.name);
	            if (!metaModule) {
	                metaModule = {
	                    name: module.fullModuleName ?? module.name,
	                    instances: [],
	                    componentRef: module.componentRef,
	                };
	                if (!metaModule.componentRef ||
	                    project.components[metaModule.componentRef]?.metadata?.['ki_keywords'] === 'power-flag') {
	                    metaModule.hideInBom = true;
	                }
	                project.modules.push(metaModule);
	            }
	            if (!metaModule.name) {
	                metaModule.name = module.name;
	            }
	            if (module.metadata && Object.keys(module.metadata).length > 0) {
	                if (!metaModule.metadata) {
	                    metaModule.metadata = {};
	                }
	                metaModule.metadata = {
	                    ...metaModule.metadata,
	                    ...module.metadata,
	                };
	            }
	            const drawPropertyValue = module.drawProperties['Value'];
	            if (drawPropertyValue) {
	                drawPropertyValue.value = (0, Util_1.getValueFromMetadata)(drawPropertyValue.value, metaModule);
	            }
	            if (metaModule.value) {
	                metaModule.value = (0, Util_1.getValueFromMetadata)(metaModule.value, metaModule);
	            }
	            if (value) {
	                if (metaModule.value && metaModule.value !== value) {
	                    const useValues = Array.from(new Set([metaModule.value, value])).filter((v) => !isEmptyValue(v));
	                    if (useValues.length === 1) {
	                        metaModule.value = useValues[0];
	                    }
	                    else {
	                        model.warnings.push(`Module ${module.name} has multiple values: ${metaModule.value}, ${value}`);
	                        if (module.metadata && Object.prototype.hasOwnProperty.call(module.metadata, 'value')) {
	                            module.metadata.value = metaModule.value;
	                        }
	                        module.drawProperties.value.value = metaModule.value;
	                    }
	                }
	                else {
	                    metaModule.value = value;
	                }
	            }
	            metaModule.instances.push({
	                ref: module.ref,
	                designId,
	            });
	            model.modules.push(module);
	        }
	    }
	    for (const component of Object.values(project.components)) {
	        component.numPads = KicadComponentPadCount_1.KicadComponentPadCount.deriveNumPads(component.footprints);
	    }
	    const netNames = new Set(nets.map((n) => n.value));
	    for (const net of nets) {
	        const netInfo = {
	            name: net.value,
	            isGlobal: false,
	            isHierarchical: false,
	            connections: model.modules
	                .map((m) => {
	                const connections = [];
	                for (const [pin, pinNet] of Object.entries(m.pinNets)) {
	                    if (pinNet === net.value && !connections.find((c) => c.moduleRef === m.ref && c.pinRef === pin)) {
	                        connections.push({
	                            moduleRef: m.ref,
	                            pinRef: pin,
	                            moduleName: m.name,
	                        });
	                    }
	                }
	                return connections;
	            })
	                .flat(),
	        };
	        model.nets[net.value] = netInfo;
	        (0, v6schema_1.kicadAddNetDifferentialPairInfo)(net.value, netInfo, netNames, netRefCtx, model.warnings);
	    }
	    const bbox = new Area_1.default();
	    bbox.extendFromPcbModel(project, model, project.layerSets[layers_1.KICAD_BRD_LAYERSET], model.warnings);
	    model.edges_bbox = {
	        minx: bbox.xMin,
	        miny: bbox.yMin,
	        maxx: bbox.xMax,
	        maxy: bbox.yMax,
	    };
	    const visibleBbox = new Area_1.default();
	    visibleBbox.extendFromPcbModel(project, model, project.layerSets[layers_1.KICAD_BRD_LAYERSET], model.warnings, false);
	    model.initial_zoombox = {
	        minx: visibleBbox.xMin,
	        miny: visibleBbox.yMin,
	        maxx: visibleBbox.xMax,
	        maxy: visibleBbox.yMax,
	    };
	    (0, Util_1.dedupeWarnings)(model);
	    layerSelector = undefined;
	    return model;
	}
	function initBoardLayerSelector(s) {
	    return (layerSelector = s || new layers_1.KicadPcbLayerSelector());
	}
	function setBoardLayerSelector(s) {
	    layerSelector = s;
	}
	function readBoardLayer(node) {
	    const index = parseInt(node.name);
	    let [name, attribute] = node.values;
	    name = (0, Util_1.removeQuotes)(name);
	    let defaultVisible;
	    let friendlyName;
	    for (const value of node.values) {
	        if (value.startsWith('"')) {
	            const unquoted = (0, Util_1.removeQuotes)(value);
	            if (unquoted !== name) {
	                friendlyName = unquoted;
	            }
	        }
	        else if (value === 'hide' || value === 'hidden') {
	            defaultVisible = false;
	        }
	    }
	    return { id: index, name, friendlyName, attribute, defaultVisible };
	}
	function readModule(project, model, node, activeLayers) {
	    let value;
	    const footprint = {
	        ref: (0, Util_1.removeQuotes)(node.values[0]),
	        name: (0, Util_1.removeQuotes)(node.values[0]),
	        pads: [],
	        drawings: [],
	    };
	    const module = {
	        ref: '',
	        footprintRef: footprint.ref,
	        componentRef: footprint.ref,
	        center: [0, 0],
	        transform: {
	            pos: [0, 0],
	            angle: 0,
	            mirrored: [],
	        },
	        layer: 'F',
	        name: '',
	        pinNets: {},
	        drawProperties: {},
	    };
	    function addMetadata(key, value, obj = footprint) {
	        value = (0, Util_1.cleanMetadataVal)(value);
	        if (!value || value === '~') {
	            return;
	        }
	        if (!obj.metadata) {
	            obj.metadata = {};
	        }
	        obj.metadata[key] = value;
	    }
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'at': {
	                const x = parseFloat(n.values[0]);
	                const y = parseFloat(n.values[1]);
	                const rotation = n.values.length > 2 ? parseFloat(n.values[2]) : 0;
	                module.center = [(0, Transform_1.roundToMaxOutputPositionPrecision)(x), (0, Transform_1.roundToMaxOutputPositionPrecision)(y)];
	                module.transform.angle = (0, Transform_1.roundToMaxOutputAnglePrecision)(rotation);
	                break;
	            }
	            case 'property':
	            case 'fp_text': {
	                const obj = readText(n);
	                let drawPropertyName = obj.text.ref;
	                let drawProperty;
	                if (obj.text.ref && obj.text.ref_designator) {
	                    if (obj.text.ref.toLowerCase() === 'user') {
	                        drawPropertyName = obj.text.ref = `u:${obj.text.value}`;
	                    }
	                    drawProperty = {
	                        value: obj.text.value,
	                    };
	                    if (obj.hidden) {
	                        drawProperty.hidden = obj.hidden;
	                    }
	                }
	                if (drawProperty && drawPropertyName) {
	                    module.drawProperties[drawPropertyName] = {
	                        pos: obj.text.pos,
	                        angle: obj.text.angle,
	                        isPositionOffset: true,
	                        rejustifyOnRotate: obj.text.rejustifyOnRotate,
	                        ...drawProperty,
	                    };
	                    addMetadata(drawPropertyName, drawProperty.value, module);
	                }
	                if (obj.text.ref?.toLowerCase() === 'reference') {
	                    module.name = obj.text.value;
	                }
	                if (obj.text.val) {
	                    value = obj.text.value.trim();
	                }
	                if (obj.text.ref) {
	                    obj.text.value = '';
	                }
	                const layers = layerSelector.filterLayers(obj.layer);
	                footprint.drawings.push({
	                    layers: layers.map((l) => l.id),
	                    drawing: obj.text,
	                });
	                break;
	            }
	            case 'fp_circle': {
	                const obj = (0, v6ParseCircle_1.readCircle)(n);
	                const layers = layerSelector.filterLayers(obj.layer);
	                footprint.drawings.push({
	                    layers: layers.map((l) => l.id),
	                    drawing: obj.circle,
	                });
	                break;
	            }
	            case 'fp_line': {
	                const obj = readLine(n);
	                const layers = layerSelector.filterLayers(obj.layer);
	                footprint.drawings.push({
	                    layers: layers.map((l) => l.id),
	                    drawing: obj.segment,
	                });
	                break;
	            }
	            case 'fp_poly': {
	                const poly = readPolygon(n, true);
	                if (poly.layer) {
	                    footprint.drawings.push({
	                        layers: layerSelector.filterLayers(poly.layer).map((l) => l.id),
	                        drawing: poly.polygon,
	                    });
	                }
	                break;
	            }
	            case 'arc':
	            case 'gr_arc': {
	                const drawing = (0, v6ParseArc_1.parseArc)(false, node, true, model.warnings, undefined, layerSelector);
	                if (drawing) {
	                    footprint.drawings.push(drawing);
	                }
	                break;
	            }
	            case 'pad': {
	                const { pad, net } = readPad(model, n, module.transform.angle, activeLayers);
	                footprint.pads.push(pad);
	                if (pad.ref && net && net.length > 0) {
	                    module.pinNets[pad.ref] = connectToSchematicNet(project, model, module, pad, net);
	                }
	                break;
	            }
	            case 'uuid': {
	                if (n.values.length > 0) {
	                    module.ref = (0, Util_1.removeQuotes)(n.values[0]);
	                }
	                break;
	            }
	            case 'path': {
	                if (n.values.length > 0) {
	                    module.ref = (0, Util_1.removeQuotes)(n.values[0]);
	                }
	                break;
	            }
	            case 'descr':
	            case 'tags':
	            case 'attr':
	                addMetadata(n.name, (0, Util_1.removeQuotes)(n.values[0]));
	                break;
	            case 'layer': {
	                const layer = layerSelector?.filterLayers((0, Util_1.removeQuotes)(n.values[0]))?.find((l) => l.face);
	                if (layer?.face) {
	                    module.layer = layer.face;
	                }
	                break;
	            }
	        }
	    });
	    for (const [key, value] of Object.entries(module.drawProperties)) {
	        if (key.startsWith('u:${')) {
	            const refMatch = key.substring(4, key.length - 1).toLowerCase();
	            if (module.drawProperties[refMatch]) {
	                value.value = module.drawProperties[refMatch].value;
	            }
	            else {
	                model.warnings.push(`Could not resolve value for ${key} on fp ${module.ref}: ${refMatch}`);
	            }
	        }
	        else if (key.startsWith('u:%')) {
	            switch (key.toLowerCase()) {
	                case 'u:%r':
	                    value.value = module.drawProperties['reference'].value;
	                    break;
	                case 'u:%v':
	                    value.value = module.drawProperties['value'].value;
	                    break;
	                default:
	                    model.warnings.push(`Unknown user variable: ${key} on fp ${module.ref}`);
	            }
	        }
	        value.angle = (0, Transform_1.roundToMaxOutputAnglePrecision)((0, Transform_1.clampRotation)(-module.transform.angle + (value.angle || 0)));
	    }
	    return { footprint, module, value };
	}
	function readText(node) {
	    let x = -1;
	    let y = -1;
	    let fontSize = -1;
	    let thickness = -1;
	    let layerName = '';
	    let rotation = 0;
	    let attr;
	    let unlocked = false;
	    const refType = (0, Util_1.removeQuotes)(node.values[0].toLowerCase());
	    const value = (0, Util_1.removeQuotes)(node.values[1]);
	    const hidden = (0, nodeIsHidden_1.nodeIsHidden)(node);
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'at': {
	                x = parseFloat(n.values[0]);
	                y = parseFloat(n.values[1]);
	                unlocked = n.values.indexOf('unlocked') !== -1;
	                rotation = n.values.length > 2 && n.values[2] !== 'unlocked' ? parseFloat(n.values[2]) : 0;
	                break;
	            }
	            case 'effects': {
	                const fontNode = n.nodes.find((n) => n.name === 'font');
	                if (fontNode) {
	                    const sizeNode = fontNode.nodes.filter((n) => n.name === 'size');
	                    if (sizeNode?.length) {
	                        fontSize = parseFloat(sizeNode[0].values[0]);
	                    }
	                    const thicknessNode = fontNode.nodes.filter((n) => n.name === 'thickness');
	                    if (thicknessNode?.length) {
	                        thickness = parseFloat(thicknessNode[0].values[0]);
	                    }
	                    if (fontNode.values.indexOf('bold') !== -1) {
	                        if (!attr) {
	                            attr = [];
	                        }
	                        attr.push('bold');
	                    }
	                }
	                break;
	            }
	            case 'layer': {
	                layerName = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	        }
	    });
	    const text = {
	        type: 'text',
	        pos: [x, y],
	        angle: rotation,
	        attr,
	        justify: [0, 0],
	        value: value,
	        size: fontSize,
	        thickness,
	    };
	    if (!unlocked) {
	        text.rejustifyOnRotate = true;
	    }
	    if (refType) {
	        text.ref = refType;
	        text.ref_designator = true;
	        if (refType === 'value') {
	            text.val = true;
	        }
	    }
	    return { text, layer: layerName, hidden };
	}
	function parseTrack(node) {
	    let xi = -1;
	    let xf = -1;
	    let yi = -1;
	    let yf = -1;
	    let layer = '';
	    let width = 0;
	    let net = '';
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'start': {
	                xi = parseFloat(n.values[0]);
	                yi = parseFloat(n.values[1]);
	                break;
	            }
	            case 'end': {
	                xf = parseFloat(n.values[0]);
	                yf = parseFloat(n.values[1]);
	                break;
	            }
	            case 'layer': {
	                layer = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	            case 'width': {
	                width = parseFloat(n.values[0]);
	                break;
	            }
	            case 'net': {
	                net = n.values[0];
	                break;
	            }
	        }
	    });
	    const track = {
	        type: 'track',
	        start: [xi, yi],
	        end: [xf, yf],
	        width,
	        net,
	        layer: -1,
	    };
	    return { layer, track };
	}
	function readVia(node, modelLayers) {
	    let center = [0, 0];
	    let size = 0;
	    let drillSize = 0;
	    let layers = [];
	    let net = '';
	    const [type] = node.values;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'at': {
	                center = [parseFloat(n.values[0]), parseFloat(n.values[1])];
	                break;
	            }
	            case 'size': {
	                size = parseFloat(n.values[0]);
	                break;
	            }
	            case 'drill': {
	                drillSize = parseFloat(n.values[0]);
	                break;
	            }
	            case 'layers': {
	                const [start, end] = n.values
	                    .map((l) => (0, Util_1.removeQuotes)(l))
	                    .map((l) => layerSelector.filterLayers(l))
	                    .flat()
	                    .map((l) => l.id)
	                    .sort();
	                layers = modelLayers.filter((l) => l.id >= start && l.id <= end).map((l) => l.id);
	                break;
	            }
	            case 'net': {
	                net = n.values[0];
	                break;
	            }
	        }
	    });
	    return {
	        type: 'via',
	        center,
	        diameter: size,
	        drillDiameter: drillSize,
	        layers,
	        net,
	        shape: 'circle',
	        metadata: {
	            type,
	        },
	    };
	}
	function readLine(node) {
	    let xi = -1;
	    let xf = -1;
	    let yi = -1;
	    let yf = -1;
	    let layer = '';
	    let width = 0;
	    let style;
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'start': {
	                xi = parseFloat(n.values[0]);
	                yi = parseFloat(n.values[1]);
	                break;
	            }
	            case 'end': {
	                xf = parseFloat(n.values[0]);
	                yf = parseFloat(n.values[1]);
	                break;
	            }
	            case 'layer': {
	                layer = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	            case 'width': {
	                width = parseFloat(n.values[0]);
	                break;
	            }
	            case 'stroke': {
	                const widthNode = n.nodes.find((n2) => n2.name === 'width');
	                if (widthNode) {
	                    width = parseFloat(widthNode.values[0]);
	                }
	                const typeNode = n.nodes.find((n2) => n2.name === 'type');
	                if (typeNode) {
	                    style = (0, v6Common_1.parseKicadLineType)(typeNode.values[0]);
	                }
	                break;
	            }
	        }
	    });
	    const segment = {
	        type: 'segment',
	        start: [xi, yi],
	        end: [xf, yf],
	        width,
	        style,
	    };
	    return { layer, segment };
	}
	function readPolygon(node, isModule = false) {
	    const { points, strokeWidth, style, fillType, layerName } = (0, v6ExtractPolygon_1.extractPolygon)(isModule, node, true);
	    const polygon = {
	        type: 'polygon',
	        filled: fillType !== 'none',
	        width: (0, Utils_1.max)(strokeWidth, 0),
	        points,
	        lineStyle: style,
	    };
	    return {
	        polygon,
	        layer: layerName,
	    };
	}
	function readPad(model, node, parentRotation, activeLayers) {
	    let x = 0, y = 0;
	    let rotation = 0;
	    let sizeX = 0, sizeY = 0;
	    let layers = [];
	    let net = '';
	    let hasDrill = false;
	    let isOval = false;
	    let drillX = 0;
	    let drillY = 0;
	    let [value, padType, shape] = node.values;
	    value = (0, Util_1.removeQuotes)(value);
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'at': {
	                x = parseFloat(n.values[0]);
	                y = parseFloat(n.values[1]);
	                rotation = n.values.length > 2 ? parseFloat(n.values[2]) : 0;
	                break;
	            }
	            case 'size': {
	                sizeX = parseFloat(n.values[0]);
	                sizeY = parseFloat(n.values[1]);
	                break;
	            }
	            case 'layers': {
	                layers = n.values
	                    .map((l) => (0, Util_1.removeQuotes)(l))
	                    .map((l) => layerSelector.filterLayers(l))
	                    .flat();
	                break;
	            }
	            case 'drill': {
	                hasDrill = true;
	                isOval = n.values[0] === 'oval';
	                drillX = isOval ? parseFloat(n.values[1]) : parseFloat(n.values[0]);
	                drillY = n.values.length > 2 ? parseFloat(n.values[2]) : drillX;
	                break;
	            }
	            case 'net': {
	                net = (0, Util_1.removeQuotes)(n.values[1]);
	                break;
	            }
	        }
	    });
	    const isPad1 = ['1', 'A', 'A1', 'P1', 'PAD1'].includes(value);
	    let angle = rotation - parentRotation;
	    while (angle >= 360) {
	        angle -= 360;
	    }
	    while (angle < 0) {
	        angle += 360;
	    }
	    const layersFormatted = layers.map((l) => l.id);
	    const pad = {
	        type: 'pad',
	        ref: value,
	        name: value,
	        layers: layersFormatted.filter((s, index) => layersFormatted.indexOf(s) === index && activeLayers.has(s)),
	        pos: [x, y],
	        angle,
	        pin1: isPad1 ? true : undefined,
	        shape: shape,
	        padType: ['thru_hole', 'np_thru_hole'].includes(padType) ? 'th' : 'smd',
	        diameter: [sizeX, sizeY],
	    };
	    switch (pad.shape) {
	        case 'circle':
	        case 'rect':
	            break;
	        case 'oval':
	            pad.shape = 'roundrect';
	            pad.radius = Math.round((0, Utils_1.min)(sizeX, sizeY) * Math.PI * 100);
	            break;
	        case 'roundrect': {
	            const radius = node.nodes.find((n) => n.name === 'roundrect_rratio')?.values[0];
	            if (radius) {
	                pad.radius = parseFloat(radius) * (0, Utils_1.min)(sizeX, sizeY);
	            }
	            break;
	        }
	        case 'custom':
	        default:
	            model.warnings.push(`Kicad pad shape '${shape}' not supported yet. Rendering as rect.`);
	            pad.shape = 'rect';
	            break;
	    }
	    if (hasDrill) {
	        pad.drillshape = isOval ? 'oblong' : 'circle';
	        pad.drillDiameter = [drillX, drillY];
	    }
	    return { pad, net };
	}
	function readZone(node) {
	    let layers = [];
	    let isFilled = false;
	    let netName = '';
	    let minThickness = 0;
	    let filledAreaThickness = false;
	    const results = [];
	    node.nodes.forEach((n) => {
	        switch (n.name) {
	            case 'net_name': {
	                netName = (0, Util_1.removeQuotes)(n.values[0]);
	                break;
	            }
	            case 'min_thickness': {
	                minThickness = parseFloat(n.values[0]);
	                break;
	            }
	            case 'filled_areas_thickness': {
	                filledAreaThickness = n.values[0] === 'yes';
	                break;
	            }
	            case 'layer':
	            case 'layers': {
	                layers = n.values
	                    .map((l) => (0, Util_1.removeQuotes)(l))
	                    .map((l) => layerSelector.filterLayers(l))
	                    .flat()
	                    .map((l) => l.id);
	                break;
	            }
	            case 'fill': {
	                isFilled = n.values[0] === 'yes';
	                break;
	            }
	            case 'polygon': {
	                const polygon = [];
	                const pts = n.nodes.find((child) => child.name === 'pts');
	                pts?.nodes.forEach((pt) => polygon.push([parseFloat(pt.values[0]), parseFloat(pt.values[1])]));
	                break;
	            }
	            case 'filled_polygon': {
	                const layer = n.nodes.find((child) => child.name === 'layer');
	                const pts = n.nodes.find((child) => child.name === 'pts');
	                if (!pts) {
	                    break;
	                }
	                const polyLayers = layer ? layerSelector.filterLayers((0, Util_1.removeQuotes)(layer.values[0])).map((l) => l.id) : layers;
	                polyLayers.forEach((l) => {
	                    results.push({
	                        layer: l,
	                        polygon: closePolygon({
	                            points: pts.nodes.map((pt) => [parseFloat(pt.values[0]), parseFloat(pt.values[1])]),
	                            filled: true,
	                            lineWidth: 0,
	                        }),
	                    });
	                });
	                break;
	            }
	        }
	    });
	    return results.map((r) => ({
	        type: 'zone',
	        polygons: [r.polygon],
	        net: netName,
	        layer: r.layer,
	        metadata: {
	            isFilled,
	            minThickness,
	            filledAreaThickness,
	        },
	    }));
	}
	function parseBoardSetup(nodes, warnings) {
	    const setup = {
	        stackup: {
	            layers: [],
	        },
	        clearances: {},
	        pcbPlotParams: {},
	        properties: {},
	    };
	    if (!nodes) {
	        warnings.push(`Kicad PCB did not contain setup information`);
	        return setup;
	    }
	    nodes.forEach((n) => {
	        switch (n.name) {
	            case 'pad_to_mask_clearance':
	                setup.clearances[n.name] = parseFloat(n.values[0]);
	                break;
	            case 'pcbplotparams': {
	                n.nodes.forEach((n2) => {
	                    const value = n2.values[0];
	                    if (value === 'true' || value === 'false') {
	                        setup.pcbPlotParams[n2.name] = value === 'true';
	                    }
	                    else if (value.startsWith('"')) {
	                        setup.pcbPlotParams[n2.name] = (0, Util_1.removeQuotes)(value);
	                    }
	                    else if (value.length > 0 &&
	                        /^-?[0-9]*\.?[0-9]*$/.test(value) &&
	                        value.indexOf('.') === value.lastIndexOf('.')) {
	                        setup.pcbPlotParams[n2.name] = parseFloat(value);
	                    }
	                    else if (value.length > 0 && /^0x[0-9a-f]+(_[0-9a-f]+)*$/.test(value)) {
	                        setup.pcbPlotParams[n2.name] = parseInt(value.substring(2).replace('_', ''), 16);
	                    }
	                    else {
	                        warnings.push(`Could not parse pcb plt param ${n2.name}`);
	                    }
	                });
	                break;
	            }
	            case 'stackup': {
	                n.nodes.forEach((n2) => {
	                    switch (n2.name) {
	                        case 'layer': {
	                            setup.stackup.layers.push(parseBoardStackupLayer(n2, warnings));
	                            break;
	                        }
	                        case 'copper_finish': {
	                            setup.stackup.copperFinish = n2.values[0];
	                            break;
	                        }
	                        case 'dielectric_constraints': {
	                            setup.stackup.dialectricConstraints = n2.values[0] !== 'no';
	                            break;
	                        }
	                        default: {
	                            warnings.push(`Unknown stack param: '${n2.name}'`);
	                            break;
	                        }
	                    }
	                });
	                break;
	            }
	            default: {
	                if (n.values.length === 1) {
	                    let value = n.values[0];
	                    if (n.values[0] === 'yes' || n.values[0] === 'no') {
	                        value = value === 'yes';
	                    }
	                    else if (Number.isFinite(parseFloat(value))) {
	                        value = parseFloat(value);
	                    }
	                    setup.properties[n.name] = value;
	                }
	                else if (n.values.length > 1) {
	                    setup.properties[n.name] = n.values.map((value) => (0, Util_1.removeQuotes)(value)).join(' ');
	                }
	                break;
	            }
	        }
	    });
	    return setup;
	}
	function parseBoardStackupLayer(node, warnings) {
	    const layer = {
	        layerName: node.values[0],
	        otherProps: {},
	        type: '',
	    };
	    node.nodes.forEach((n) => {
	        let value = n.values[0];
	        if (value === 'true' || value === 'false') {
	            value = value === 'true';
	        }
	        else if (value.startsWith('"')) {
	            value = (0, Util_1.removeQuotes)(value);
	        }
	        else if (value.length > 0 && /^-?[0-9]*\.?[0-9]*$/.test(value) && value.indexOf('.') === value.lastIndexOf('.')) {
	            value = parseFloat(value);
	        }
	        else {
	            warnings.push(`Could not parse pcb plt param ${n.name}`);
	        }
	        switch (n.name) {
	            case 'thickness':
	            case 'material':
	            case 'epsilon_r':
	            case 'loss_tangent':
	            case 'type': {
	                layer[n.name] = value;
	                break;
	            }
	            default:
	                warnings.push(`Unknown pcb layer prop ${n.name}`);
	        }
	    });
	    return layer;
	}
	function closePolygon(poly) {
	    const lastPoint = poly.points[poly.points.length - 1];
	    if (poly.points[0][0] !== lastPoint[0] || poly.points[0][1] !== lastPoint[1]) {
	        poly.points.push(poly.points[0]);
	    }
	    return poly;
	}
	function isEmptyValue(value) {
	    return value === undefined || value === null;
	}
	function connectToSchematicNet(project, model, module, pad, net) {
	    if (!project) {
	        return net;
	    }
	    const netWithSlash = net.startsWith('/') ? net : `/${net}`;
	    const component = project.components[module.componentRef];
	    if (!component) {
	        model.warnings.push(`Could not find component ${module.componentRef} for module ${module.ref}`);
	        return net;
	    }
	    const signal = component.signals.find((s) => s.pinRefs.includes(pad.ref));
	    if (!signal) {
	        model.warnings.push(`Could not find signal for pin ${pad.ref} on component ${component.name} for module ${module.ref}`);
	        return net;
	    }
	    const metaModule = project.modules.find((m) => m.name === module.name);
	    if (!metaModule) {
	        model.warnings.push(`Could not find meta module ${module.name} for module ${module.ref}`);
	        return net;
	    }
	    for (const instance of metaModule.instances) {
	        const module = project.designs[instance.designId].modules.find((m) => m.ref === instance.ref);
	        if (!module) {
	            model.warnings.push(`Could not find module ${instance.ref} in design ${instance.designId}`);
	            continue;
	        }
	        for (const padRef of signal.padRefs) {
	            if (!padRef) {
	                continue;
	            }
	            const ref = module.pinNets[padRef];
	            if (!ref) {
	                continue;
	            }
	            if (ref.endsWith(netWithSlash)) {
	                return ref;
	            }
	            if ((0, sigma_engine_shared_1.isNetRef)(ref)) {
	                ref.setName(netWithSlash);
	                return ref;
	            }
	        }
	    }
	    return net;
	}
	
	return board$1;
}

var symbol_lib = {};

var kicadFileType = {};

var hasRequiredKicadFileType;

function requireKicadFileType () {
	if (hasRequiredKicadFileType) return kicadFileType;
	hasRequiredKicadFileType = 1;
	Object.defineProperty(kicadFileType, "__esModule", { value: true });
	kicadFileType.KicadFileType = void 0;
	var KicadFileType;
	(function (KicadFileType) {
	    KicadFileType["EESchemaBrd"] = ".brd";
	    KicadFileType["KicadPcb"] = ".kicad_pcb";
	    KicadFileType["KicadSch"] = ".kicad_sch";
	    KicadFileType["EESchemaSch"] = ".sch";
	    KicadFileType["EESchemaLib"] = ".lib";
	    KicadFileType["KicadFootprintLibTable"] = "fp-lib-table";
	    KicadFileType["KicadSymbolLibTable"] = "sym-lib-table";
	    KicadFileType["KicadDocLib"] = ".dcm";
	    KicadFileType["KicadProject"] = ".kicad_pro";
	    KicadFileType["KicadProjectLocalSettings"] = ".kicad_prl";
	    KicadFileType["KicadSymbol"] = ".kicad_sym";
	    KicadFileType["KicadModule"] = ".kicad_mod";
	    KicadFileType["KicadNetlist"] = ".net";
	})(KicadFileType || (kicadFileType.KicadFileType = KicadFileType = {}));
	
	return kicadFileType;
}

var hasRequiredSymbol_lib;

function requireSymbol_lib () {
	if (hasRequiredSymbol_lib) return symbol_lib;
	hasRequiredSymbol_lib = 1;
	Object.defineProperty(symbol_lib, "__esModule", { value: true });
	symbol_lib.parseSymbolLibrary = parseSymbolLibrary;
	symbol_lib.parseSymbolLibraryWithFootprints = parseSymbolLibraryWithFootprints;
	const Util_1 = requireUtil();
	const sigma_engine_shared_1 = requireDist$1();
	const layers_1 = requireLayers$2();
	const v6schema_1 = requireV6schema();
	const kicad_file_type_1 = requireKicadFileType();
	const board_1 = requireBoard$2();
	const NodeParser_1 = requireNodeParser();
	const KicadComponentPadCount_1 = requireKicadComponentPadCount();
	function parseSymbolLibrary(filename, contents, parser = new NodeParser_1.SExpressionFileParser()) {
	    const layerSelector = new layers_1.KicadSchLayerSelector();
	    const model = {
	        modelType: 'library',
	        name: filename,
	        layerSets: {
	            [layers_1.KICAD_SCH_LAYERSET]: {
	                type: 'layerSet',
	                layerSetType: 'schematic',
	                id: layers_1.KICAD_SCH_LAYERSET,
	                layers: layerSelector.getLayers(),
	            },
	        },
	        components: {},
	        metadata: {},
	        warnings: [],
	    };
	    const tree = parser.parseFile(filename, contents);
	    (0, v6schema_1.setLayerSelector)(layerSelector);
	    (0, v6schema_1.parseComponents)(model, tree, new sigma_engine_shared_1.NetRefCtx());
	    tree.nodes.forEach((node) => {
	        switch (node.name) {
	            case 'generator':
	            case 'version': {
	                model.metadata[node.name] = node.values[0];
	                break;
	            }
	        }
	    });
	    (0, Util_1.dedupeWarnings)(model);
	    (0, v6schema_1.setLayerSelector)(undefined);
	    return model;
	}
	function parseSymbolLibraryWithFootprints(files, parser = new NodeParser_1.SExpressionFileParser()) {
	    const symbolFile = files.find((f) => f.name.endsWith(kicad_file_type_1.KicadFileType.KicadSymbol));
	    if (!symbolFile) {
	        throw new Error('No symbol file found');
	    }
	    const schLayerSelector = new layers_1.KicadSchLayerSelector();
	    const brdLayerSelector = new layers_1.KicadPcbLayerSelector();
	    const model = {
	        modelType: 'library',
	        name: symbolFile.name,
	        layerSets: {
	            [layers_1.KICAD_SCH_LAYERSET]: {
	                type: 'layerSet',
	                layerSetType: 'schematic',
	                id: layers_1.KICAD_SCH_LAYERSET,
	                layers: schLayerSelector.getLayers(),
	            },
	            [layers_1.KICAD_BRD_LAYERSET]: {
	                type: 'layerSet',
	                layerSetType: 'pcb',
	                id: layers_1.KICAD_BRD_LAYERSET,
	                layers: brdLayerSelector.getLayers(),
	            },
	        },
	        components: {},
	        metadata: {},
	        warnings: [],
	    };
	    const tree = parser.parseFile(symbolFile.name, symbolFile.contents);
	    (0, v6schema_1.setLayerSelector)(schLayerSelector);
	    (0, v6schema_1.parseComponents)(model, tree, new sigma_engine_shared_1.NetRefCtx());
	    tree.nodes.forEach((node) => {
	        switch (node.name) {
	            case 'generator':
	            case 'version': {
	                model.metadata[node.name] = node.values[0];
	                break;
	            }
	        }
	    });
	    const footprintFiles = files
	        .filter((f) => f.name.endsWith(kicad_file_type_1.KicadFileType.KicadModule))
	        .sort((a, b) => a.name.localeCompare(b.name));
	    const parsedFootprints = {};
	    for (const footprintFile of footprintFiles) {
	        const tree = parser.parseFile(footprintFile.name, footprintFile.contents);
	        (0, board_1.setBoardLayerSelector)(brdLayerSelector);
	        const { footprint } = (0, board_1.readModule)(undefined, model, tree, new Set(brdLayerSelector.getLayers().map((l) => l.id)));
	        parsedFootprints[footprintFile.name] = footprint;
	        footprint.ref = footprintFile.name
	            .split(/[/\\]/)
	            .pop()
	            .substr(0, footprintFile.name.length - kicad_file_type_1.KicadFileType.KicadModule.length);
	    }
	    for (const component of Object.values(model.components)) {
	        component.metadata = component.metadata ?? {};
	        component.metadata.filename = symbolFile.name;
	        if (typeof component.metadata.ki_fp_filters === 'string') {
	            const filters = component.metadata.ki_fp_filters.split(' ');
	            for (const filter of filters) {
	                const fpFiles = footprintFiles.find((f) => f.name.endsWith(filter + kicad_file_type_1.KicadFileType.KicadModule));
	                if (fpFiles) {
	                    component.footprints.push(parsedFootprints[fpFiles.name]);
	                }
	            }
	        }
	        else {
	            for (const parsed of Object.values(parsedFootprints)) {
	                component.footprints.push(parsed);
	            }
	        }
	        component.numPads = KicadComponentPadCount_1.KicadComponentPadCount.deriveNumPads(component.footprints);
	    }
	    (0, Util_1.dedupeWarnings)(model);
	    (0, v6schema_1.setLayerSelector)(undefined);
	    return model;
	}
	
	return symbol_lib;
}

var IKicadComponentResolver = {};

var hasRequiredIKicadComponentResolver;

function requireIKicadComponentResolver () {
	if (hasRequiredIKicadComponentResolver) return IKicadComponentResolver;
	hasRequiredIKicadComponentResolver = 1;
	Object.defineProperty(IKicadComponentResolver, "__esModule", { value: true });
	
	return IKicadComponentResolver;
}

var kicadNetlistParser = {};

var hasRequiredKicadNetlistParser;

function requireKicadNetlistParser () {
	if (hasRequiredKicadNetlistParser) return kicadNetlistParser;
	hasRequiredKicadNetlistParser = 1;
	Object.defineProperty(kicadNetlistParser, "__esModule", { value: true });
	kicadNetlistParser.parseKicadNetlist = parseKicadNetlist;
	const NodeParser_1 = requireNodeParser();
	const Util_1 = requireUtil();
	function parseKicadNetlist(sourceFile, contents, designId = 'kicad-netlist') {
	    const warnings = [];
	    try {
	        const rootNode = NodeParser_1.SExpressionFileParser.Parse(contents);
	        if (rootNode.name !== 'export') {
	            warnings.push(`Expected root node 'export', found '${rootNode.name}'`);
	        }
	        const schematic = {
	            modelType: 'schematic',
	            designId,
	            designName: 'KiCad Netlist',
	            sourceFile,
	            sheetIndex: 0,
	            grid: { distance: 50, units: 'mm' },
	            edges_bbox: {
	                minx: Number.POSITIVE_INFINITY,
	                miny: Number.POSITIVE_INFINITY,
	                maxx: Number.NEGATIVE_INFINITY,
	                maxy: Number.NEGATIVE_INFINITY,
	            },
	            initial_zoombox: {
	                minx: Number.POSITIVE_INFINITY,
	                miny: Number.POSITIVE_INFINITY,
	                maxx: Number.NEGATIVE_INFINITY,
	                maxy: Number.NEGATIVE_INFINITY,
	            },
	            layerSet: 'default',
	            drawings: {},
	            modules: [],
	            nets: {},
	            warnings: [],
	            ports: {},
	            metadata: {
	                title: '',
	                revision: '',
	                company: '',
	                date: '',
	            },
	        };
	        const designNode = rootNode.nodes.find((n) => n.name === 'design');
	        const componentsNode = rootNode.nodes.find((n) => n.name === 'components');
	        const netsNode = rootNode.nodes.find((n) => n.name === 'nets');
	        if (designNode) {
	            parseDesignMetadata(designNode, schematic);
	        }
	        const componentMap = new Map();
	        if (componentsNode) {
	            parseComponents(componentsNode, componentMap);
	        }
	        else {
	            throw new Error('No components found in netlist');
	        }
	        for (const [ref, compInfo] of componentMap) {
	            const module = {
	                ref,
	                name: ref,
	                footprintRef: compInfo.footprint || 'Unknown',
	                componentRef: ref,
	                center: [0, 0],
	                layer: 'F',
	                transform: { angle: 0, mirrored: [false, false] },
	                pinNets: {},
	                drawProperties: {},
	            };
	            schematic.modules.push(module);
	        }
	        if (netsNode) {
	            parseNets(netsNode, schematic);
	        }
	        else {
	            throw new Error('No nets found in netlist');
	        }
	        return { schematic, warnings };
	    }
	    catch (error) {
	        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
	        warnings.push(`Failed to parse KiCad netlist: ${errorMsg}`);
	        throw new Error(`KiCad netlist parsing failed: ${errorMsg}`);
	    }
	}
	function parseDesignMetadata(designNode, schematic) {
	    for (const child of designNode.nodes) {
	        switch (child.name) {
	            case 'source':
	                schematic.metadata.source = child.values[0] ?? '';
	                break;
	            case 'date':
	                schematic.metadata.date = child.values[0] ?? '';
	                break;
	            case 'tool':
	                schematic.metadata.title = child.values[0] ?? 'KiCad';
	                break;
	        }
	    }
	}
	function parseComponents(componentsNode, componentMap) {
	    for (const compNode of componentsNode.nodes) {
	        if (compNode.name !== 'comp') {
	            continue;
	        }
	        let ref = '';
	        let value = '';
	        let footprint = '';
	        for (const child of compNode.nodes) {
	            if (child.name === 'ref') {
	                ref = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	            }
	            else if (child.name === 'value') {
	                value = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	            }
	            else if (child.name === 'footprint') {
	                footprint = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	            }
	        }
	        if (ref) {
	            componentMap.set(ref, { value, footprint });
	        }
	    }
	}
	function writesNumberedPinFunctions(tool) {
	    const major = /^Eeschema\s+(\d+)\./.exec((0, Util_1.removeQuotes)(tool))?.[1];
	    return major !== undefined && Number(major) >= 10;
	}
	function parseNets(netsNode, schematic) {
	    const tool = schematic.metadata?.title;
	    const numberedPinFunctions = writesNumberedPinFunctions(typeof tool === 'string' ? tool : '');
	    for (const netNode of netsNode.nodes) {
	        if (netNode.name !== 'net') {
	            continue;
	        }
	        let netName = '';
	        for (const child of netNode.nodes) {
	            if (child.name === 'name') {
	                netName = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	            }
	        }
	        const connections = [];
	        for (const nodeChild of netNode.nodes) {
	            if (nodeChild.name === 'node') {
	                let ref = '';
	                let pin = '';
	                let pinFunction;
	                for (const child of nodeChild.nodes) {
	                    if (child.name === 'ref') {
	                        ref = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	                    }
	                    else if (child.name === 'pin') {
	                        pin = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	                    }
	                    else if (child.name === 'pinfunction') {
	                        pinFunction = (0, Util_1.removeQuotes)(child.values[0] ?? '');
	                    }
	                }
	                if (pinFunction && numberedPinFunctions && pinFunction.endsWith(`_${pin}`)) {
	                    pinFunction = pinFunction.slice(0, -(pin.length + 1));
	                }
	                if (ref && pin) {
	                    connections.push({
	                        moduleRef: ref,
	                        moduleName: ref,
	                        pinRef: pin,
	                        ...(pinFunction ? { pinFunction } : {}),
	                    });
	                    const module = schematic.modules.find((m) => m.ref === ref);
	                    if (module) {
	                        module.pinNets[pin] = netName;
	                    }
	                }
	            }
	        }
	        schematic.nets[netName] = {
	            name: netName,
	            connections,
	        };
	    }
	}
	
	return kicadNetlistParser;
}

var kicadNetlistValidation = {};

var applyNetlistToProject = {};

var hasRequiredApplyNetlistToProject;

function requireApplyNetlistToProject () {
	if (hasRequiredApplyNetlistToProject) return applyNetlistToProject;
	hasRequiredApplyNetlistToProject = 1;
	Object.defineProperty(applyNetlistToProject, "__esModule", { value: true });
	applyNetlistToProject.applyNetlistToProject = applyNetlistToProject$1;
	const sigma_engine_shared_1 = requireDist$1();
	const sigma_engine_shared_2 = requireDist$1();
	function applyNetlistToProject$1(projectModel, netlistModel) {
	    const netlistMigrationResult = {
	        type: sigma_engine_shared_2.DesignModels.MigrationResultType.AltiumProjectTransformation,
	        message: 'Applied netlist to project',
	        innerResults: [],
	        isModified: false,
	        entity: projectModel,
	    };
	    try {
	        if (!netlistModel) {
	            throw new Error('Netlist model is null or undefined');
	        }
	        const validationResult = validateNetlistCompatibility(projectModel, netlistModel);
	        if (validationResult.type === 'error') {
	            return {
	                type: sigma_engine_shared_2.DesignModels.MigrationResultType.AltiumProjectTransformation,
	                message: validationResult.message,
	                innerResults: [validationResult],
	                isModified: false,
	                severity: sigma_engine_shared_2.DesignModels.BoundaryLevel.Error,
	                entity: null,
	            };
	        }
	        const workingModel = structuredClone(projectModel);
	        const updateResult = updateNetsFromNetlist(workingModel, netlistModel);
	        netlistMigrationResult.innerResults.push(updateResult);
	        netlistMigrationResult.entity = workingModel;
	        netlistMigrationResult.isModified = true;
	        const successResult = {
	            type: 'info',
	            message: 'Successfully applied netlist to project model',
	        };
	        netlistMigrationResult.innerResults.push(successResult);
	    }
	    catch (error) {
	        const errorResult = {
	            type: 'error',
	            severity: sigma_engine_shared_2.DesignModels.BoundaryLevel.Error,
	            message: `Failed to apply netlist to project: ${error instanceof Error ? error.message : String(error)}`,
	            stack: error instanceof Error ? error.stack : undefined,
	        };
	        netlistMigrationResult.innerResults.push(errorResult);
	        netlistMigrationResult.message = errorResult.message;
	        netlistMigrationResult.entity = null;
	        netlistMigrationResult.isModified = false;
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(netlistMigrationResult);
	    return netlistMigrationResult;
	}
	function validateNetlistCompatibility(projectModel, netlistModel) {
	    const netlistModuleNames = new Set(netlistModel.modules.map((m) => m.name));
	    const projectModuleNames = new Set();
	    const hasSchematicDesigns = Object.values(projectModel.designs).some((design) => design.modelType === 'schematic');
	    for (const module of projectModel.modules) {
	        if (module.name.trim() === '') {
	            continue;
	        }
	        const inSchematic = module.instances.find(({ designId }) => projectModel.designs[designId]?.modelType === 'schematic');
	        if (!inSchematic) {
	            continue;
	        }
	        projectModuleNames.add(module.name);
	    }
	    if (hasSchematicDesigns) {
	        const missingModules = [];
	        for (const netlistModuleName of netlistModuleNames) {
	            if (!projectModuleNames.has(netlistModuleName)) {
	                missingModules.push(netlistModuleName);
	            }
	        }
	        if (missingModules.length > 0) {
	            return {
	                type: 'error',
	                message: `Netlist contains modules not found in project model: ${missingModules.join(', ')}`,
	            };
	        }
	        const expectedModules = [];
	        for (const projectModuleName of projectModuleNames) {
	            if (netlistModuleNames.has(projectModuleName)) {
	                continue;
	            }
	            const projectModule = projectModel.modules.find((m) => m.name === projectModuleName);
	            if (!projectModule) {
	                throw new Error(`Missing project module ${projectModuleName}`);
	            }
	            const component = projectModel.components[projectModule.componentRef];
	            if (!component) {
	                throw new Error(`Missing component ${projectModule.componentRef} for module ${projectModule.name}`);
	            }
	            if (!component.isVirtual && component.numPads !== 0 && component.numPins !== 0) {
	                expectedModules.push(projectModuleName);
	            }
	        }
	        if (expectedModules.length > 0) {
	            return {
	                type: 'error',
	                message: `Project model contains non-virtual modules not found in netlist: ${expectedModules.join(', ')}`,
	            };
	        }
	    }
	    return {
	        type: 'info',
	        message: 'Netlist compatibility validation passed',
	    };
	}
	function updateNetsFromNetlist(projectModel, netlistModel) {
	    const result = {
	        type: 'info',
	        message: 'Updated nets from netlist',
	        innerResults: [],
	    };
	    projectModel.globalNets = {};
	    for (const design of Object.values(projectModel.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        try {
	            updateDesignNetsFromNetlist(design, netlistModel, result);
	        }
	        catch (error) {
	            result.innerResults.push({
	                type: 'error',
	                severity: sigma_engine_shared_2.DesignModels.BoundaryLevel.Error,
	                message: `Failed to apply netlist to design ${design.designId}: ${error instanceof Error ? error.message : String(error)}`,
	            });
	        }
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function updateDesignNetsFromNetlist(design, netlistModel, result) {
	    const pendingMessages = [];
	    const netNameMap = new Map();
	    for (const module of design.modules) {
	        const moduleName = module.fullModuleName ?? module.name;
	        const inNetlist = netlistModel.modules.some((m) => m.name === moduleName);
	        if (!inNetlist) {
	            continue;
	        }
	        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
	            const existingNetName = netName.valueOf();
	            const netlistModule = netlistModel.modules.find((m) => m.name === moduleName);
	            if (!netlistModule) {
	                throw new Error(`Missing netlist module ${moduleName}`);
	            }
	            let netlistNetName = netlistModule.pinNets[pinRef]?.valueOf();
	            if (netlistNetName === undefined) {
	                const pinRefWithUnderscores = Object.keys(netlistModule.pinNets).find((k) => k.replace(/ /g, '_') === pinRef);
	                if (pinRefWithUnderscores !== undefined) {
	                    netlistNetName = netlistModule.pinNets[pinRefWithUnderscores];
	                }
	                if (netlistNetName === undefined) {
	                    pendingMessages.push({
	                        type: 'error',
	                        message: `Missing netlist net for pin ${pinRef} on module ${moduleName}`,
	                    });
	                    continue;
	                }
	            }
	            if (netNameMap.has(existingNetName)) {
	                if (netNameMap.get(existingNetName) !== netlistNetName) {
	                    throw new Error(`Net name mismatch for pin ${pinRef} on module ${moduleName}: ${netNameMap.get(existingNetName)} vs ${netlistNetName} (${existingNetName})`);
	                }
	            }
	            else {
	                netNameMap.set(existingNetName, netlistNetName);
	                if (existingNetName !== netlistNetName) {
	                    pendingMessages.push({
	                        type: 'net-name-updated',
	                        message: `Net name updated from ${existingNetName} to ${netlistNetName} on module ${moduleName} pin ${pinRef}`,
	                        oldNetName: existingNetName,
	                        newNetName: netlistNetName,
	                    });
	                }
	            }
	        }
	    }
	    const oldNets = design.nets;
	    const newNets = {};
	    const pinNetWrites = [];
	    for (const [oldNetName, newNetName] of netNameMap.entries()) {
	        if (!(oldNetName in oldNets)) {
	            throw new Error(`Missing net ${oldNetName} in design ${design.designId}`);
	        }
	        if (newNetName in newNets) {
	            newNets[newNetName] = {
	                ...newNets[newNetName],
	                ...oldNets[oldNetName],
	                name: newNetName,
	                connections: newNets[newNetName].connections,
	            };
	            for (const connection of oldNets[oldNetName].connections) {
	                if (!newNets[newNetName].connections.some((c) => c.moduleRef === connection.moduleRef && c.pinRef === connection.pinRef)) {
	                    newNets[newNetName].connections.push(connection);
	                    const module = design.modules.find((m) => m.ref === connection.moduleRef);
	                    if (!module) {
	                        throw new Error(`Missing module ${connection.moduleRef} in design ${design.designId}`);
	                    }
	                    pinNetWrites.push({ module, pinRef: connection.pinRef, netName: newNetName });
	                }
	            }
	        }
	        else {
	            newNets[newNetName] = {
	                ...oldNets[oldNetName],
	                name: newNetName,
	                connections: [...oldNets[oldNetName].connections],
	                isGlobal: true,
	            };
	            for (const connection of newNets[newNetName].connections) {
	                const module = design.modules.find((m) => m.ref === connection.moduleRef);
	                if (!module) {
	                    throw new Error(`Missing module ${connection.moduleRef} in design ${design.designId}`);
	                }
	                pinNetWrites.push({ module, pinRef: connection.pinRef, netName: newNetName });
	            }
	        }
	    }
	    design.nets = newNets;
	    for (const write of pinNetWrites) {
	        write.module.pinNets[write.pinRef] = write.netName;
	    }
	    result.innerResults.push(...pendingMessages);
	    for (const module of design.modules) {
	        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
	            if (!(netName in design.nets)) {
	                design.nets[netName] = {
	                    ...oldNets[netName],
	                    name: netName,
	                    connections: [],
	                };
	            }
	            const netInfo = design.nets[netName];
	            if (!netInfo.connections.some((c) => c.moduleRef === module.ref && c.pinRef === pinRef)) {
	                netInfo.connections.push({
	                    moduleRef: module.ref,
	                    moduleName: module.name,
	                    pinRef,
	                });
	            }
	        }
	    }
	}
	
	return applyNetlistToProject;
}

var hasRequiredKicadNetlistValidation;

function requireKicadNetlistValidation () {
	if (hasRequiredKicadNetlistValidation) return kicadNetlistValidation;
	hasRequiredKicadNetlistValidation = 1;
	Object.defineProperty(kicadNetlistValidation, "__esModule", { value: true });
	kicadNetlistValidation.validateKicadNetlist = validateKicadNetlist;
	kicadNetlistValidation.applyKicadNetlistToProject = applyKicadNetlistToProject;
	kicadNetlistValidation.selectKicadNetlistFile = selectKicadNetlistFile;
	kicadNetlistValidation.getSchematicFromResult = getSchematicFromResult;
	const sigma_engine_shared_1 = requireDist$1();
	const kicadNetlistParser_1 = requireKicadNetlistParser();
	const constants_1 = requireConstants();
	const applyNetlistToProject_1 = requireApplyNetlistToProject();
	function validateKicadNetlist(filename, fileContents) {
	    try {
	        const result = (0, kicadNetlistParser_1.parseKicadNetlist)(filename, fileContents);
	        return {
	            metadata: {
	                [constants_1.KicadNetlistSchematicModel]: result.schematic,
	            },
	            referencedFiles: [],
	            version: '1.0',
	        };
	    }
	    catch (error) {
	        return {
	            skipReason: `Failed to parse KiCad netlist: ${error instanceof Error ? error.message : String(error)}`,
	            metadata: {},
	            referencedFiles: [],
	        };
	    }
	}
	function applyKicadNetlistToProject(projectDefinition, projectModel) {
	    const netlistFile = selectKicadNetlistFile(projectDefinition);
	    if (!netlistFile) {
	        return Promise.resolve({
	            type: sigma_engine_shared_1.DesignModels.MigrationResultType.ProjectMigration,
	            message: 'No netlist file found',
	            innerResults: [],
	            isModified: false,
	            entity: null,
	        });
	    }
	    const netlistModel = netlistFile.validationResult.data.metadata[constants_1.KicadNetlistSchematicModel];
	    const serializedProject = toSerializedProjectModel(projectModel);
	    const result = (0, applyNetlistToProject_1.applyNetlistToProject)(serializedProject, resolveKicadNetlistPins(serializedProject, netlistModel));
	    return Promise.resolve({
	        ...result,
	        type: sigma_engine_shared_1.DesignModels.MigrationResultType.ProjectMigration,
	    });
	}
	function resolveKicadNetlistPins(projectModel, netlistModel) {
	    const resolvedNetlist = structuredClone(netlistModel);
	    const netlistModulesByName = new Map();
	    for (const candidate of resolvedNetlist.modules) {
	        if (!netlistModulesByName.has(candidate.name)) {
	            netlistModulesByName.set(candidate.name, candidate);
	        }
	    }
	    const nodesByModuleAndNumber = new Map();
	    for (const [netName, net] of Object.entries(netlistModel.nets)) {
	        for (const connection of net.connections) {
	            const nodesByNumber = nodesByModuleAndNumber.get(connection.moduleName) ??
	                new Map();
	            const nodes = nodesByNumber.get(connection.pinRef) ?? [];
	            nodes.push({ netName, pinFunction: connection.pinFunction });
	            nodesByNumber.set(connection.pinRef, nodes);
	            nodesByModuleAndNumber.set(connection.moduleName, nodesByNumber);
	        }
	    }
	    for (const design of Object.values(projectModel.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        for (const module of design.modules) {
	            const netlistModuleName = module.fullModuleName ?? module.name;
	            const netlistModule = netlistModulesByName.get(netlistModuleName);
	            const component = projectModel.components[module.componentRef];
	            const symbol = component?.symbols.find((candidate) => candidate.name === module.footprintRef);
	            if (!netlistModule || !symbol) {
	                continue;
	            }
	            const nodesByNumber = nodesByModuleAndNumber.get(netlistModuleName) ?? new Map();
	            const resolvedPinNets = { ...netlistModule.pinNets };
	            for (const [pinNumber, nodes] of nodesByNumber) {
	                const pins = symbol.pins.filter((pin) => pin.number === pinNumber);
	                if (pins.length === 0) {
	                    continue;
	                }
	                if (nodes.length === 1) {
	                    for (const pin of pins) {
	                        resolvedPinNets[pin.ref] = nodes[0].netName;
	                    }
	                    continue;
	                }
	                const unmatchedPins = new Set(pins);
	                for (const node of nodes) {
	                    const matchingPins = node.pinFunction ? pins.filter((pin) => pin.name === node.pinFunction) : [];
	                    if (matchingPins.length === 1) {
	                        resolvedPinNets[matchingPins[0].ref] = node.netName;
	                        unmatchedPins.delete(matchingPins[0]);
	                    }
	                }
	                const unmatchedNodes = nodes.filter((node) => !node.pinFunction || pins.filter((pin) => pin.name === node.pinFunction).length !== 1);
	                for (const [index, pin] of [...unmatchedPins].entries()) {
	                    const node = unmatchedNodes[index];
	                    if (node) {
	                        resolvedPinNets[pin.ref] = node.netName;
	                    }
	                }
	            }
	            netlistModule.pinNets = resolvedPinNets;
	        }
	    }
	    return resolvedNetlist;
	}
	function toSerializedProjectModel(projectModel) {
	    return JSON.parse(JSON.stringify(projectModel));
	}
	function selectKicadNetlistFile(projectDefinition) {
	    const validNetlists = Object.values(projectDefinition.other ?? {}).filter((f) => f.type === 'netlist' && f.validationResult?.isValid);
	    if (validNetlists.length <= 1) {
	        return validNetlists[0];
	    }
	    const projectFile = Object.values(projectDefinition.other ?? {}).find((f) => f.type === 'project' && f.originalFileName.toLowerCase().endsWith('.kicad_pro'));
	    const projectBasename = projectFile ? getBasenameWithoutExtension(projectFile.originalFileName).toLowerCase() : undefined;
	    const [best] = [...validNetlists].sort((a, b) => compareNetlistCandidates(a, b, projectBasename));
	    return best;
	}
	function compareNetlistCandidates(a, b, projectBasename) {
	    const aMatchesProject = matchesProjectBasename(a, projectBasename);
	    const bMatchesProject = matchesProjectBasename(b, projectBasename);
	    if (aMatchesProject !== bMatchesProject) {
	        return aMatchesProject ? -1 : 1;
	    }
	    const depthDiff = pathDepth(a.originalFileName) - pathDepth(b.originalFileName);
	    if (depthDiff !== 0) {
	        return depthDiff;
	    }
	    if (a.originalFileName < b.originalFileName) {
	        return -1;
	    }
	    if (a.originalFileName > b.originalFileName) {
	        return 1;
	    }
	    return 0;
	}
	function matchesProjectBasename(f, projectBasename) {
	    return (projectBasename !== undefined && getBasenameWithoutExtension(f.originalFileName).toLowerCase() === projectBasename);
	}
	function getBasenameWithoutExtension(fileName) {
	    const lastSlashIndex = fileName.lastIndexOf('/');
	    const base = lastSlashIndex >= 0 ? fileName.substring(lastSlashIndex + 1) : fileName;
	    const lastDotIndex = base.lastIndexOf('.');
	    return lastDotIndex >= 0 ? base.substring(0, lastDotIndex) : base;
	}
	function pathDepth(fileName) {
	    return fileName.split('/').length - 1;
	}
	function getSchematicFromResult(result) {
	    if (result.skipReason) {
	        return undefined;
	    }
	    if (!result.metadata[constants_1.KicadNetlistSchematicModel]) {
	        return undefined;
	    }
	    return result.metadata[constants_1.KicadNetlistSchematicModel];
	}
	
	return kicadNetlistValidation;
}

var hasRequiredKicad;

function requireKicad () {
	if (hasRequiredKicad) return Kicad;
	hasRequiredKicad = 1;
	(function (exports) {
		var __createBinding = (Kicad && Kicad.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Kicad && Kicad.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBoard$2(), exports);
		__exportStar(requireSchematic$1(), exports);
		__exportStar(requireLayers$2(), exports);
		__exportStar(requireProject$2(), exports);
		__exportStar(requireSymbol_lib(), exports);
		__exportStar(requireIKicadComponentResolver(), exports);
		__exportStar(requireComponentResolver(), exports);
		__exportStar(requireKicadNetlistParser(), exports);
		__exportStar(requireKicadNetlistValidation(), exports);
		__exportStar(requireKicadTextCodec(), exports);
		
	} (Kicad));
	return Kicad;
}

var Eagle = {};

var board = {};

var sax = {};

// shim for using process in browser
// based off https://github.com/defunctzombie/node-process/blob/master/browser.js

function defaultSetTimout() {
    throw new Error('setTimeout has not been defined');
}
function defaultClearTimeout () {
    throw new Error('clearTimeout has not been defined');
}
var cachedSetTimeout = defaultSetTimout;
var cachedClearTimeout = defaultClearTimeout;
if (typeof global.setTimeout === 'function') {
    cachedSetTimeout = setTimeout;
}
if (typeof global.clearTimeout === 'function') {
    cachedClearTimeout = clearTimeout;
}

function runTimeout(fun) {
    if (cachedSetTimeout === setTimeout) {
        //normal enviroments in sane situations
        return setTimeout(fun, 0);
    }
    // if setTimeout wasn't available but was latter defined
    if ((cachedSetTimeout === defaultSetTimout || !cachedSetTimeout) && setTimeout) {
        cachedSetTimeout = setTimeout;
        return setTimeout(fun, 0);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedSetTimeout(fun, 0);
    } catch(e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't trust the global object when called normally
            return cachedSetTimeout.call(null, fun, 0);
        } catch(e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error
            return cachedSetTimeout.call(this, fun, 0);
        }
    }


}
function runClearTimeout(marker) {
    if (cachedClearTimeout === clearTimeout) {
        //normal enviroments in sane situations
        return clearTimeout(marker);
    }
    // if clearTimeout wasn't available but was latter defined
    if ((cachedClearTimeout === defaultClearTimeout || !cachedClearTimeout) && clearTimeout) {
        cachedClearTimeout = clearTimeout;
        return clearTimeout(marker);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedClearTimeout(marker);
    } catch (e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't  trust the global object when called normally
            return cachedClearTimeout.call(null, marker);
        } catch (e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error.
            // Some versions of I.E. have different rules for clearTimeout vs setTimeout
            return cachedClearTimeout.call(this, marker);
        }
    }



}
var queue = [];
var draining = false;
var currentQueue;
var queueIndex = -1;

function cleanUpNextTick() {
    if (!draining || !currentQueue) {
        return;
    }
    draining = false;
    if (currentQueue.length) {
        queue = currentQueue.concat(queue);
    } else {
        queueIndex = -1;
    }
    if (queue.length) {
        drainQueue();
    }
}

function drainQueue() {
    if (draining) {
        return;
    }
    var timeout = runTimeout(cleanUpNextTick);
    draining = true;

    var len = queue.length;
    while(len) {
        currentQueue = queue;
        queue = [];
        while (++queueIndex < len) {
            if (currentQueue) {
                currentQueue[queueIndex].run();
            }
        }
        queueIndex = -1;
        len = queue.length;
    }
    currentQueue = null;
    draining = false;
    runClearTimeout(timeout);
}
function nextTick(fun) {
    var args = new Array(arguments.length - 1);
    if (arguments.length > 1) {
        for (var i = 1; i < arguments.length; i++) {
            args[i - 1] = arguments[i];
        }
    }
    queue.push(new Item(fun, args));
    if (queue.length === 1 && !draining) {
        runTimeout(drainQueue);
    }
}
// v8 likes predictible objects
function Item(fun, array) {
    this.fun = fun;
    this.array = array;
}
Item.prototype.run = function () {
    this.fun.apply(null, this.array);
};
var title = 'browser';
var platform = 'browser';
var browser$1 = true;
var env = {};
var argv = [];
var version = ''; // empty string to avoid regexp issues
var versions = {};
var release = {};
var config = {};

function noop() {}

var on = noop;
var addListener = noop;
var once = noop;
var off = noop;
var removeListener = noop;
var removeAllListeners = noop;
var emit = noop;

function binding(name) {
    throw new Error('process.binding is not supported');
}

function cwd () { return '/' }
function chdir (dir) {
    throw new Error('process.chdir is not supported');
}function umask() { return 0; }

// from https://github.com/kumavis/browser-process-hrtime/blob/master/index.js
var performance$1 = global.performance || {};
var performanceNow =
  performance$1.now        ||
  performance$1.mozNow     ||
  performance$1.msNow      ||
  performance$1.oNow       ||
  performance$1.webkitNow  ||
  function(){ return (new Date()).getTime() };

// generate timestamp or delta
// see http://nodejs.org/api/process.html#process_process_hrtime
function hrtime(previousTimestamp){
  var clocktime = performanceNow.call(performance$1)*1e-3;
  var seconds = Math.floor(clocktime);
  var nanoseconds = Math.floor((clocktime%1)*1e9);
  if (previousTimestamp) {
    seconds = seconds - previousTimestamp[0];
    nanoseconds = nanoseconds - previousTimestamp[1];
    if (nanoseconds<0) {
      seconds--;
      nanoseconds += 1e9;
    }
  }
  return [seconds,nanoseconds]
}

var startTime = new Date();
function uptime() {
  var currentTime = new Date();
  var dif = currentTime - startTime;
  return dif / 1000;
}

var browser$1$1 = {
  nextTick: nextTick,
  title: title,
  browser: browser$1,
  env: env,
  argv: argv,
  version: version,
  versions: versions,
  on: on,
  addListener: addListener,
  once: once,
  off: off,
  removeListener: removeListener,
  removeAllListeners: removeAllListeners,
  emit: emit,
  binding: binding,
  cwd: cwd,
  chdir: chdir,
  umask: umask,
  hrtime: hrtime,
  platform: platform,
  release: release,
  config: config,
  uptime: uptime
};

var process$1 = browser$1$1;

var inherits;
if (typeof Object.create === 'function'){
  inherits = function inherits(ctor, superCtor) {
    // implementation from standard node.js 'util' module
    ctor.super_ = superCtor;
    ctor.prototype = Object.create(superCtor.prototype, {
      constructor: {
        value: ctor,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
  };
} else {
  inherits = function inherits(ctor, superCtor) {
    ctor.super_ = superCtor;
    var TempCtor = function () {};
    TempCtor.prototype = superCtor.prototype;
    ctor.prototype = new TempCtor();
    ctor.prototype.constructor = ctor;
  };
}
var inherits$1 = inherits;

// Copyright Joyent, Inc. and other Node contributors.
var formatRegExp = /%[sdj%]/g;
function format(f) {
  if (!isString(f)) {
    var objects = [];
    for (var i = 0; i < arguments.length; i++) {
      objects.push(inspect(arguments[i]));
    }
    return objects.join(' ');
  }

  var i = 1;
  var args = arguments;
  var len = args.length;
  var str = String(f).replace(formatRegExp, function(x) {
    if (x === '%%') return '%';
    if (i >= len) return x;
    switch (x) {
      case '%s': return String(args[i++]);
      case '%d': return Number(args[i++]);
      case '%j':
        try {
          return JSON.stringify(args[i++]);
        } catch (_) {
          return '[Circular]';
        }
      default:
        return x;
    }
  });
  for (var x = args[i]; i < len; x = args[++i]) {
    if (isNull(x) || !isObject(x)) {
      str += ' ' + x;
    } else {
      str += ' ' + inspect(x);
    }
  }
  return str;
}

// Mark that a method should not be used.
// Returns a modified function which warns once by default.
// If --no-deprecation is set, then it is a no-op.
function deprecate(fn, msg) {
  // Allow for deprecating things in the process of starting up.
  if (isUndefined(global.process)) {
    return function() {
      return deprecate(fn, msg).apply(this, arguments);
    };
  }

  if (process$1.noDeprecation === true) {
    return fn;
  }

  var warned = false;
  function deprecated() {
    if (!warned) {
      if (process$1.throwDeprecation) {
        throw new Error(msg);
      } else if (process$1.traceDeprecation) {
        console.trace(msg);
      } else {
        console.error(msg);
      }
      warned = true;
    }
    return fn.apply(this, arguments);
  }

  return deprecated;
}

var debugs = {};
var debugEnviron;
function debuglog(set) {
  if (isUndefined(debugEnviron))
    debugEnviron = process$1.env.NODE_DEBUG || '';
  set = set.toUpperCase();
  if (!debugs[set]) {
    if (new RegExp('\\b' + set + '\\b', 'i').test(debugEnviron)) {
      var pid = 0;
      debugs[set] = function() {
        var msg = format.apply(null, arguments);
        console.error('%s %d: %s', set, pid, msg);
      };
    } else {
      debugs[set] = function() {};
    }
  }
  return debugs[set];
}

/**
 * Echos the value of a value. Trys to print the value out
 * in the best way possible given the different types.
 *
 * @param {Object} obj The object to print out.
 * @param {Object} opts Optional options object that alters the output.
 */
/* legacy: obj, showHidden, depth, colors*/
function inspect(obj, opts) {
  // default options
  var ctx = {
    seen: [],
    stylize: stylizeNoColor
  };
  // legacy...
  if (arguments.length >= 3) ctx.depth = arguments[2];
  if (arguments.length >= 4) ctx.colors = arguments[3];
  if (isBoolean(opts)) {
    // legacy...
    ctx.showHidden = opts;
  } else if (opts) {
    // got an "options" object
    _extend(ctx, opts);
  }
  // set default options
  if (isUndefined(ctx.showHidden)) ctx.showHidden = false;
  if (isUndefined(ctx.depth)) ctx.depth = 2;
  if (isUndefined(ctx.colors)) ctx.colors = false;
  if (isUndefined(ctx.customInspect)) ctx.customInspect = true;
  if (ctx.colors) ctx.stylize = stylizeWithColor;
  return formatValue(ctx, obj, ctx.depth);
}

// http://en.wikipedia.org/wiki/ANSI_escape_code#graphics
inspect.colors = {
  'bold' : [1, 22],
  'italic' : [3, 23],
  'underline' : [4, 24],
  'inverse' : [7, 27],
  'white' : [37, 39],
  'grey' : [90, 39],
  'black' : [30, 39],
  'blue' : [34, 39],
  'cyan' : [36, 39],
  'green' : [32, 39],
  'magenta' : [35, 39],
  'red' : [31, 39],
  'yellow' : [33, 39]
};

// Don't use 'blue' not visible on cmd.exe
inspect.styles = {
  'special': 'cyan',
  'number': 'yellow',
  'boolean': 'yellow',
  'undefined': 'grey',
  'null': 'bold',
  'string': 'green',
  'date': 'magenta',
  // "name": intentionally not styling
  'regexp': 'red'
};


function stylizeWithColor(str, styleType) {
  var style = inspect.styles[styleType];

  if (style) {
    return '\u001b[' + inspect.colors[style][0] + 'm' + str +
           '\u001b[' + inspect.colors[style][1] + 'm';
  } else {
    return str;
  }
}


function stylizeNoColor(str, styleType) {
  return str;
}


function arrayToHash(array) {
  var hash = {};

  array.forEach(function(val, idx) {
    hash[val] = true;
  });

  return hash;
}


function formatValue(ctx, value, recurseTimes) {
  // Provide a hook for user-specified inspect functions.
  // Check that value is an object with an inspect function on it
  if (ctx.customInspect &&
      value &&
      isFunction(value.inspect) &&
      // Filter out the util module, it's inspect function is special
      value.inspect !== inspect &&
      // Also filter out any prototype objects using the circular check.
      !(value.constructor && value.constructor.prototype === value)) {
    var ret = value.inspect(recurseTimes, ctx);
    if (!isString(ret)) {
      ret = formatValue(ctx, ret, recurseTimes);
    }
    return ret;
  }

  // Primitive types cannot have properties
  var primitive = formatPrimitive(ctx, value);
  if (primitive) {
    return primitive;
  }

  // Look up the keys of the object.
  var keys = Object.keys(value);
  var visibleKeys = arrayToHash(keys);

  if (ctx.showHidden) {
    keys = Object.getOwnPropertyNames(value);
  }

  // IE doesn't make error fields non-enumerable
  // http://msdn.microsoft.com/en-us/library/ie/dww52sbt(v=vs.94).aspx
  if (isError(value)
      && (keys.indexOf('message') >= 0 || keys.indexOf('description') >= 0)) {
    return formatError(value);
  }

  // Some type of object without properties can be shortcutted.
  if (keys.length === 0) {
    if (isFunction(value)) {
      var name = value.name ? ': ' + value.name : '';
      return ctx.stylize('[Function' + name + ']', 'special');
    }
    if (isRegExp(value)) {
      return ctx.stylize(RegExp.prototype.toString.call(value), 'regexp');
    }
    if (isDate(value)) {
      return ctx.stylize(Date.prototype.toString.call(value), 'date');
    }
    if (isError(value)) {
      return formatError(value);
    }
  }

  var base = '', array = false, braces = ['{', '}'];

  // Make Array say that they are Array
  if (isArray$1(value)) {
    array = true;
    braces = ['[', ']'];
  }

  // Make functions say that they are functions
  if (isFunction(value)) {
    var n = value.name ? ': ' + value.name : '';
    base = ' [Function' + n + ']';
  }

  // Make RegExps say that they are RegExps
  if (isRegExp(value)) {
    base = ' ' + RegExp.prototype.toString.call(value);
  }

  // Make dates with properties first say the date
  if (isDate(value)) {
    base = ' ' + Date.prototype.toUTCString.call(value);
  }

  // Make error with message first say the error
  if (isError(value)) {
    base = ' ' + formatError(value);
  }

  if (keys.length === 0 && (!array || value.length == 0)) {
    return braces[0] + base + braces[1];
  }

  if (recurseTimes < 0) {
    if (isRegExp(value)) {
      return ctx.stylize(RegExp.prototype.toString.call(value), 'regexp');
    } else {
      return ctx.stylize('[Object]', 'special');
    }
  }

  ctx.seen.push(value);

  var output;
  if (array) {
    output = formatArray(ctx, value, recurseTimes, visibleKeys, keys);
  } else {
    output = keys.map(function(key) {
      return formatProperty(ctx, value, recurseTimes, visibleKeys, key, array);
    });
  }

  ctx.seen.pop();

  return reduceToSingleString(output, base, braces);
}


function formatPrimitive(ctx, value) {
  if (isUndefined(value))
    return ctx.stylize('undefined', 'undefined');
  if (isString(value)) {
    var simple = '\'' + JSON.stringify(value).replace(/^"|"$/g, '')
                                             .replace(/'/g, "\\'")
                                             .replace(/\\"/g, '"') + '\'';
    return ctx.stylize(simple, 'string');
  }
  if (isNumber(value))
    return ctx.stylize('' + value, 'number');
  if (isBoolean(value))
    return ctx.stylize('' + value, 'boolean');
  // For some reason typeof null is "object", so special case here.
  if (isNull(value))
    return ctx.stylize('null', 'null');
}


function formatError(value) {
  return '[' + Error.prototype.toString.call(value) + ']';
}


function formatArray(ctx, value, recurseTimes, visibleKeys, keys) {
  var output = [];
  for (var i = 0, l = value.length; i < l; ++i) {
    if (hasOwnProperty(value, String(i))) {
      output.push(formatProperty(ctx, value, recurseTimes, visibleKeys,
          String(i), true));
    } else {
      output.push('');
    }
  }
  keys.forEach(function(key) {
    if (!key.match(/^\d+$/)) {
      output.push(formatProperty(ctx, value, recurseTimes, visibleKeys,
          key, true));
    }
  });
  return output;
}


function formatProperty(ctx, value, recurseTimes, visibleKeys, key, array) {
  var name, str, desc;
  desc = Object.getOwnPropertyDescriptor(value, key) || { value: value[key] };
  if (desc.get) {
    if (desc.set) {
      str = ctx.stylize('[Getter/Setter]', 'special');
    } else {
      str = ctx.stylize('[Getter]', 'special');
    }
  } else {
    if (desc.set) {
      str = ctx.stylize('[Setter]', 'special');
    }
  }
  if (!hasOwnProperty(visibleKeys, key)) {
    name = '[' + key + ']';
  }
  if (!str) {
    if (ctx.seen.indexOf(desc.value) < 0) {
      if (isNull(recurseTimes)) {
        str = formatValue(ctx, desc.value, null);
      } else {
        str = formatValue(ctx, desc.value, recurseTimes - 1);
      }
      if (str.indexOf('\n') > -1) {
        if (array) {
          str = str.split('\n').map(function(line) {
            return '  ' + line;
          }).join('\n').substr(2);
        } else {
          str = '\n' + str.split('\n').map(function(line) {
            return '   ' + line;
          }).join('\n');
        }
      }
    } else {
      str = ctx.stylize('[Circular]', 'special');
    }
  }
  if (isUndefined(name)) {
    if (array && key.match(/^\d+$/)) {
      return str;
    }
    name = JSON.stringify('' + key);
    if (name.match(/^"([a-zA-Z_][a-zA-Z_0-9]*)"$/)) {
      name = name.substr(1, name.length - 2);
      name = ctx.stylize(name, 'name');
    } else {
      name = name.replace(/'/g, "\\'")
                 .replace(/\\"/g, '"')
                 .replace(/(^"|"$)/g, "'");
      name = ctx.stylize(name, 'string');
    }
  }

  return name + ': ' + str;
}


function reduceToSingleString(output, base, braces) {
  var length = output.reduce(function(prev, cur) {
    if (cur.indexOf('\n') >= 0) ;
    return prev + cur.replace(/\u001b\[\d\d?m/g, '').length + 1;
  }, 0);

  if (length > 60) {
    return braces[0] +
           (base === '' ? '' : base + '\n ') +
           ' ' +
           output.join(',\n  ') +
           ' ' +
           braces[1];
  }

  return braces[0] + base + ' ' + output.join(', ') + ' ' + braces[1];
}


// NOTE: These type checking functions intentionally don't use `instanceof`
// because it is fragile and can be easily faked with `Object.create()`.
function isArray$1(ar) {
  return Array.isArray(ar);
}

function isBoolean(arg) {
  return typeof arg === 'boolean';
}

function isNull(arg) {
  return arg === null;
}

function isNumber(arg) {
  return typeof arg === 'number';
}

function isString(arg) {
  return typeof arg === 'string';
}

function isUndefined(arg) {
  return arg === void 0;
}

function isRegExp(re) {
  return isObject(re) && objectToString(re) === '[object RegExp]';
}

function isObject(arg) {
  return typeof arg === 'object' && arg !== null;
}

function isDate(d) {
  return isObject(d) && objectToString(d) === '[object Date]';
}

function isError(e) {
  return isObject(e) &&
      (objectToString(e) === '[object Error]' || e instanceof Error);
}

function isFunction(arg) {
  return typeof arg === 'function';
}

function objectToString(o) {
  return Object.prototype.toString.call(o);
}

function _extend(origin, add) {
  // Don't do anything if add isn't an object
  if (!add || !isObject(add)) return origin;

  var keys = Object.keys(add);
  var i = keys.length;
  while (i--) {
    origin[keys[i]] = add[keys[i]];
  }
  return origin;
}
function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

var lookup = [];
var revLookup = [];
var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array;
var inited = false;
function init () {
  inited = true;
  var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  for (var i = 0, len = code.length; i < len; ++i) {
    lookup[i] = code[i];
    revLookup[code.charCodeAt(i)] = i;
  }

  revLookup['-'.charCodeAt(0)] = 62;
  revLookup['_'.charCodeAt(0)] = 63;
}

function toByteArray (b64) {
  if (!inited) {
    init();
  }
  var i, j, l, tmp, placeHolders, arr;
  var len = b64.length;

  if (len % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4')
  }

  // the number of equal signs (place holders)
  // if there are two placeholders, than the two characters before it
  // represent one byte
  // if there is only one, then the three characters before it represent 2 bytes
  // this is just a cheap hack to not do indexOf twice
  placeHolders = b64[len - 2] === '=' ? 2 : b64[len - 1] === '=' ? 1 : 0;

  // base64 is 4/3 + up to two characters of the original data
  arr = new Arr(len * 3 / 4 - placeHolders);

  // if there are placeholders, only get up to the last complete 4 chars
  l = placeHolders > 0 ? len - 4 : len;

  var L = 0;

  for (i = 0, j = 0; i < l; i += 4, j += 3) {
    tmp = (revLookup[b64.charCodeAt(i)] << 18) | (revLookup[b64.charCodeAt(i + 1)] << 12) | (revLookup[b64.charCodeAt(i + 2)] << 6) | revLookup[b64.charCodeAt(i + 3)];
    arr[L++] = (tmp >> 16) & 0xFF;
    arr[L++] = (tmp >> 8) & 0xFF;
    arr[L++] = tmp & 0xFF;
  }

  if (placeHolders === 2) {
    tmp = (revLookup[b64.charCodeAt(i)] << 2) | (revLookup[b64.charCodeAt(i + 1)] >> 4);
    arr[L++] = tmp & 0xFF;
  } else if (placeHolders === 1) {
    tmp = (revLookup[b64.charCodeAt(i)] << 10) | (revLookup[b64.charCodeAt(i + 1)] << 4) | (revLookup[b64.charCodeAt(i + 2)] >> 2);
    arr[L++] = (tmp >> 8) & 0xFF;
    arr[L++] = tmp & 0xFF;
  }

  return arr
}

function tripletToBase64 (num) {
  return lookup[num >> 18 & 0x3F] + lookup[num >> 12 & 0x3F] + lookup[num >> 6 & 0x3F] + lookup[num & 0x3F]
}

function encodeChunk (uint8, start, end) {
  var tmp;
  var output = [];
  for (var i = start; i < end; i += 3) {
    tmp = (uint8[i] << 16) + (uint8[i + 1] << 8) + (uint8[i + 2]);
    output.push(tripletToBase64(tmp));
  }
  return output.join('')
}

function fromByteArray (uint8) {
  if (!inited) {
    init();
  }
  var tmp;
  var len = uint8.length;
  var extraBytes = len % 3; // if we have 1 byte left, pad 2 bytes
  var output = '';
  var parts = [];
  var maxChunkLength = 16383; // must be multiple of 3

  // go through the array every three bytes, we'll deal with trailing stuff later
  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
    parts.push(encodeChunk(uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)));
  }

  // pad the end with zeros, but make sure to not forget the extra bytes
  if (extraBytes === 1) {
    tmp = uint8[len - 1];
    output += lookup[tmp >> 2];
    output += lookup[(tmp << 4) & 0x3F];
    output += '==';
  } else if (extraBytes === 2) {
    tmp = (uint8[len - 2] << 8) + (uint8[len - 1]);
    output += lookup[tmp >> 10];
    output += lookup[(tmp >> 4) & 0x3F];
    output += lookup[(tmp << 2) & 0x3F];
    output += '=';
  }

  parts.push(output);

  return parts.join('')
}

function read (buffer, offset, isLE, mLen, nBytes) {
  var e, m;
  var eLen = nBytes * 8 - mLen - 1;
  var eMax = (1 << eLen) - 1;
  var eBias = eMax >> 1;
  var nBits = -7;
  var i = isLE ? (nBytes - 1) : 0;
  var d = isLE ? -1 : 1;
  var s = buffer[offset + i];

  i += d;

  e = s & ((1 << (-nBits)) - 1);
  s >>= (-nBits);
  nBits += eLen;
  for (; nBits > 0; e = e * 256 + buffer[offset + i], i += d, nBits -= 8) {}

  m = e & ((1 << (-nBits)) - 1);
  e >>= (-nBits);
  nBits += mLen;
  for (; nBits > 0; m = m * 256 + buffer[offset + i], i += d, nBits -= 8) {}

  if (e === 0) {
    e = 1 - eBias;
  } else if (e === eMax) {
    return m ? NaN : ((s ? -1 : 1) * Infinity)
  } else {
    m = m + Math.pow(2, mLen);
    e = e - eBias;
  }
  return (s ? -1 : 1) * m * Math.pow(2, e - mLen)
}

function write (buffer, value, offset, isLE, mLen, nBytes) {
  var e, m, c;
  var eLen = nBytes * 8 - mLen - 1;
  var eMax = (1 << eLen) - 1;
  var eBias = eMax >> 1;
  var rt = (mLen === 23 ? Math.pow(2, -24) - Math.pow(2, -77) : 0);
  var i = isLE ? 0 : (nBytes - 1);
  var d = isLE ? 1 : -1;
  var s = value < 0 || (value === 0 && 1 / value < 0) ? 1 : 0;

  value = Math.abs(value);

  if (isNaN(value) || value === Infinity) {
    m = isNaN(value) ? 1 : 0;
    e = eMax;
  } else {
    e = Math.floor(Math.log(value) / Math.LN2);
    if (value * (c = Math.pow(2, -e)) < 1) {
      e--;
      c *= 2;
    }
    if (e + eBias >= 1) {
      value += rt / c;
    } else {
      value += rt * Math.pow(2, 1 - eBias);
    }
    if (value * c >= 2) {
      e++;
      c /= 2;
    }

    if (e + eBias >= eMax) {
      m = 0;
      e = eMax;
    } else if (e + eBias >= 1) {
      m = (value * c - 1) * Math.pow(2, mLen);
      e = e + eBias;
    } else {
      m = value * Math.pow(2, eBias - 1) * Math.pow(2, mLen);
      e = 0;
    }
  }

  for (; mLen >= 8; buffer[offset + i] = m & 0xff, i += d, m /= 256, mLen -= 8) {}

  e = (e << mLen) | m;
  eLen += mLen;
  for (; eLen > 0; buffer[offset + i] = e & 0xff, i += d, e /= 256, eLen -= 8) {}

  buffer[offset + i - d] |= s * 128;
}

var toString = {}.toString;

var isArray = Array.isArray || function (arr) {
  return toString.call(arr) == '[object Array]';
};

/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <feross@feross.org> <http://feross.org>
 * @license  MIT
 */

var INSPECT_MAX_BYTES = 50;

/**
 * If `Buffer.TYPED_ARRAY_SUPPORT`:
 *   === true    Use Uint8Array implementation (fastest)
 *   === false   Use Object implementation (most compatible, even IE6)
 *
 * Browsers that support typed arrays are IE 10+, Firefox 4+, Chrome 7+, Safari 5.1+,
 * Opera 11.6+, iOS 4.2+.
 *
 * Due to various browser bugs, sometimes the Object implementation will be used even
 * when the browser supports typed arrays.
 *
 * Note:
 *
 *   - Firefox 4-29 lacks support for adding new properties to `Uint8Array` instances,
 *     See: https://bugzilla.mozilla.org/show_bug.cgi?id=695438.
 *
 *   - Chrome 9-10 is missing the `TypedArray.prototype.subarray` function.
 *
 *   - IE10 has a broken `TypedArray.prototype.subarray` function which returns arrays of
 *     incorrect length in some situations.

 * We detect these buggy browsers and set `Buffer.TYPED_ARRAY_SUPPORT` to `false` so they
 * get the Object implementation, which is slower but behaves correctly.
 */
Buffer$1.TYPED_ARRAY_SUPPORT = global.TYPED_ARRAY_SUPPORT !== undefined
  ? global.TYPED_ARRAY_SUPPORT
  : true;

/*
 * Export kMaxLength after typed array support is determined.
 */
kMaxLength();

function kMaxLength () {
  return Buffer$1.TYPED_ARRAY_SUPPORT
    ? 0x7fffffff
    : 0x3fffffff
}

function createBuffer (that, length) {
  if (kMaxLength() < length) {
    throw new RangeError('Invalid typed array length')
  }
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    // Return an augmented `Uint8Array` instance, for best performance
    that = new Uint8Array(length);
    that.__proto__ = Buffer$1.prototype;
  } else {
    // Fallback: Return an object instance of the Buffer class
    if (that === null) {
      that = new Buffer$1(length);
    }
    that.length = length;
  }

  return that
}

/**
 * The Buffer constructor returns instances of `Uint8Array` that have their
 * prototype changed to `Buffer.prototype`. Furthermore, `Buffer` is a subclass of
 * `Uint8Array`, so the returned instances will have all the node `Buffer` methods
 * and the `Uint8Array` methods. Square bracket notation works as expected -- it
 * returns a single octet.
 *
 * The `Uint8Array` prototype remains unmodified.
 */

function Buffer$1 (arg, encodingOrOffset, length) {
  if (!Buffer$1.TYPED_ARRAY_SUPPORT && !(this instanceof Buffer$1)) {
    return new Buffer$1(arg, encodingOrOffset, length)
  }

  // Common case.
  if (typeof arg === 'number') {
    if (typeof encodingOrOffset === 'string') {
      throw new Error(
        'If encoding is specified then the first argument must be a string'
      )
    }
    return allocUnsafe(this, arg)
  }
  return from(this, arg, encodingOrOffset, length)
}

Buffer$1.poolSize = 8192; // not used by this implementation

// TODO: Legacy, not needed anymore. Remove in next major version.
Buffer$1._augment = function (arr) {
  arr.__proto__ = Buffer$1.prototype;
  return arr
};

function from (that, value, encodingOrOffset, length) {
  if (typeof value === 'number') {
    throw new TypeError('"value" argument must not be a number')
  }

  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
    return fromArrayBuffer(that, value, encodingOrOffset, length)
  }

  if (typeof value === 'string') {
    return fromString(that, value, encodingOrOffset)
  }

  return fromObject(that, value)
}

/**
 * Functionally equivalent to Buffer(arg, encoding) but throws a TypeError
 * if value is a number.
 * Buffer.from(str[, encoding])
 * Buffer.from(array)
 * Buffer.from(buffer)
 * Buffer.from(arrayBuffer[, byteOffset[, length]])
 **/
Buffer$1.from = function (value, encodingOrOffset, length) {
  return from(null, value, encodingOrOffset, length)
};

if (Buffer$1.TYPED_ARRAY_SUPPORT) {
  Buffer$1.prototype.__proto__ = Uint8Array.prototype;
  Buffer$1.__proto__ = Uint8Array;
}

function assertSize (size) {
  if (typeof size !== 'number') {
    throw new TypeError('"size" argument must be a number')
  } else if (size < 0) {
    throw new RangeError('"size" argument must not be negative')
  }
}

function alloc (that, size, fill, encoding) {
  assertSize(size);
  if (size <= 0) {
    return createBuffer(that, size)
  }
  if (fill !== undefined) {
    // Only pay attention to encoding if it's a string. This
    // prevents accidentally sending in a number that would
    // be interpretted as a start offset.
    return typeof encoding === 'string'
      ? createBuffer(that, size).fill(fill, encoding)
      : createBuffer(that, size).fill(fill)
  }
  return createBuffer(that, size)
}

/**
 * Creates a new filled Buffer instance.
 * alloc(size[, fill[, encoding]])
 **/
Buffer$1.alloc = function (size, fill, encoding) {
  return alloc(null, size, fill, encoding)
};

function allocUnsafe (that, size) {
  assertSize(size);
  that = createBuffer(that, size < 0 ? 0 : checked(size) | 0);
  if (!Buffer$1.TYPED_ARRAY_SUPPORT) {
    for (var i = 0; i < size; ++i) {
      that[i] = 0;
    }
  }
  return that
}

/**
 * Equivalent to Buffer(num), by default creates a non-zero-filled Buffer instance.
 * */
Buffer$1.allocUnsafe = function (size) {
  return allocUnsafe(null, size)
};
/**
 * Equivalent to SlowBuffer(num), by default creates a non-zero-filled Buffer instance.
 */
Buffer$1.allocUnsafeSlow = function (size) {
  return allocUnsafe(null, size)
};

function fromString (that, string, encoding) {
  if (typeof encoding !== 'string' || encoding === '') {
    encoding = 'utf8';
  }

  if (!Buffer$1.isEncoding(encoding)) {
    throw new TypeError('"encoding" must be a valid string encoding')
  }

  var length = byteLength(string, encoding) | 0;
  that = createBuffer(that, length);

  var actual = that.write(string, encoding);

  if (actual !== length) {
    // Writing a hex string, for example, that contains invalid characters will
    // cause everything after the first invalid character to be ignored. (e.g.
    // 'abxxcd' will be treated as 'ab')
    that = that.slice(0, actual);
  }

  return that
}

function fromArrayLike (that, array) {
  var length = array.length < 0 ? 0 : checked(array.length) | 0;
  that = createBuffer(that, length);
  for (var i = 0; i < length; i += 1) {
    that[i] = array[i] & 255;
  }
  return that
}

function fromArrayBuffer (that, array, byteOffset, length) {
  array.byteLength; // this throws if `array` is not a valid ArrayBuffer

  if (byteOffset < 0 || array.byteLength < byteOffset) {
    throw new RangeError('\'offset\' is out of bounds')
  }

  if (array.byteLength < byteOffset + (length || 0)) {
    throw new RangeError('\'length\' is out of bounds')
  }

  if (byteOffset === undefined && length === undefined) {
    array = new Uint8Array(array);
  } else if (length === undefined) {
    array = new Uint8Array(array, byteOffset);
  } else {
    array = new Uint8Array(array, byteOffset, length);
  }

  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    // Return an augmented `Uint8Array` instance, for best performance
    that = array;
    that.__proto__ = Buffer$1.prototype;
  } else {
    // Fallback: Return an object instance of the Buffer class
    that = fromArrayLike(that, array);
  }
  return that
}

function fromObject (that, obj) {
  if (internalIsBuffer(obj)) {
    var len = checked(obj.length) | 0;
    that = createBuffer(that, len);

    if (that.length === 0) {
      return that
    }

    obj.copy(that, 0, 0, len);
    return that
  }

  if (obj) {
    if ((typeof ArrayBuffer !== 'undefined' &&
        obj.buffer instanceof ArrayBuffer) || 'length' in obj) {
      if (typeof obj.length !== 'number' || isnan(obj.length)) {
        return createBuffer(that, 0)
      }
      return fromArrayLike(that, obj)
    }

    if (obj.type === 'Buffer' && isArray(obj.data)) {
      return fromArrayLike(that, obj.data)
    }
  }

  throw new TypeError('First argument must be a string, Buffer, ArrayBuffer, Array, or array-like object.')
}

function checked (length) {
  // Note: cannot use `length < kMaxLength()` here because that fails when
  // length is NaN (which is otherwise coerced to zero.)
  if (length >= kMaxLength()) {
    throw new RangeError('Attempt to allocate Buffer larger than maximum ' +
                         'size: 0x' + kMaxLength().toString(16) + ' bytes')
  }
  return length | 0
}
Buffer$1.isBuffer = isBuffer;
function internalIsBuffer (b) {
  return !!(b != null && b._isBuffer)
}

Buffer$1.compare = function compare (a, b) {
  if (!internalIsBuffer(a) || !internalIsBuffer(b)) {
    throw new TypeError('Arguments must be Buffers')
  }

  if (a === b) return 0

  var x = a.length;
  var y = b.length;

  for (var i = 0, len = Math.min(x, y); i < len; ++i) {
    if (a[i] !== b[i]) {
      x = a[i];
      y = b[i];
      break
    }
  }

  if (x < y) return -1
  if (y < x) return 1
  return 0
};

Buffer$1.isEncoding = function isEncoding (encoding) {
  switch (String(encoding).toLowerCase()) {
    case 'hex':
    case 'utf8':
    case 'utf-8':
    case 'ascii':
    case 'latin1':
    case 'binary':
    case 'base64':
    case 'ucs2':
    case 'ucs-2':
    case 'utf16le':
    case 'utf-16le':
      return true
    default:
      return false
  }
};

Buffer$1.concat = function concat (list, length) {
  if (!isArray(list)) {
    throw new TypeError('"list" argument must be an Array of Buffers')
  }

  if (list.length === 0) {
    return Buffer$1.alloc(0)
  }

  var i;
  if (length === undefined) {
    length = 0;
    for (i = 0; i < list.length; ++i) {
      length += list[i].length;
    }
  }

  var buffer = Buffer$1.allocUnsafe(length);
  var pos = 0;
  for (i = 0; i < list.length; ++i) {
    var buf = list[i];
    if (!internalIsBuffer(buf)) {
      throw new TypeError('"list" argument must be an Array of Buffers')
    }
    buf.copy(buffer, pos);
    pos += buf.length;
  }
  return buffer
};

function byteLength (string, encoding) {
  if (internalIsBuffer(string)) {
    return string.length
  }
  if (typeof ArrayBuffer !== 'undefined' && typeof ArrayBuffer.isView === 'function' &&
      (ArrayBuffer.isView(string) || string instanceof ArrayBuffer)) {
    return string.byteLength
  }
  if (typeof string !== 'string') {
    string = '' + string;
  }

  var len = string.length;
  if (len === 0) return 0

  // Use a for loop to avoid recursion
  var loweredCase = false;
  for (;;) {
    switch (encoding) {
      case 'ascii':
      case 'latin1':
      case 'binary':
        return len
      case 'utf8':
      case 'utf-8':
      case undefined:
        return utf8ToBytes(string).length
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return len * 2
      case 'hex':
        return len >>> 1
      case 'base64':
        return base64ToBytes(string).length
      default:
        if (loweredCase) return utf8ToBytes(string).length // assume utf8
        encoding = ('' + encoding).toLowerCase();
        loweredCase = true;
    }
  }
}
Buffer$1.byteLength = byteLength;

function slowToString (encoding, start, end) {
  var loweredCase = false;

  // No need to verify that "this.length <= MAX_UINT32" since it's a read-only
  // property of a typed array.

  // This behaves neither like String nor Uint8Array in that we set start/end
  // to their upper/lower bounds if the value passed is out of range.
  // undefined is handled specially as per ECMA-262 6th Edition,
  // Section 13.3.3.7 Runtime Semantics: KeyedBindingInitialization.
  if (start === undefined || start < 0) {
    start = 0;
  }
  // Return early if start > this.length. Done here to prevent potential uint32
  // coercion fail below.
  if (start > this.length) {
    return ''
  }

  if (end === undefined || end > this.length) {
    end = this.length;
  }

  if (end <= 0) {
    return ''
  }

  // Force coersion to uint32. This will also coerce falsey/NaN values to 0.
  end >>>= 0;
  start >>>= 0;

  if (end <= start) {
    return ''
  }

  if (!encoding) encoding = 'utf8';

  while (true) {
    switch (encoding) {
      case 'hex':
        return hexSlice(this, start, end)

      case 'utf8':
      case 'utf-8':
        return utf8Slice(this, start, end)

      case 'ascii':
        return asciiSlice(this, start, end)

      case 'latin1':
      case 'binary':
        return latin1Slice(this, start, end)

      case 'base64':
        return base64Slice(this, start, end)

      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return utf16leSlice(this, start, end)

      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
        encoding = (encoding + '').toLowerCase();
        loweredCase = true;
    }
  }
}

// The property is used by `Buffer.isBuffer` and `is-buffer` (in Safari 5-7) to detect
// Buffer instances.
Buffer$1.prototype._isBuffer = true;

function swap (b, n, m) {
  var i = b[n];
  b[n] = b[m];
  b[m] = i;
}

Buffer$1.prototype.swap16 = function swap16 () {
  var len = this.length;
  if (len % 2 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 16-bits')
  }
  for (var i = 0; i < len; i += 2) {
    swap(this, i, i + 1);
  }
  return this
};

Buffer$1.prototype.swap32 = function swap32 () {
  var len = this.length;
  if (len % 4 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 32-bits')
  }
  for (var i = 0; i < len; i += 4) {
    swap(this, i, i + 3);
    swap(this, i + 1, i + 2);
  }
  return this
};

Buffer$1.prototype.swap64 = function swap64 () {
  var len = this.length;
  if (len % 8 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 64-bits')
  }
  for (var i = 0; i < len; i += 8) {
    swap(this, i, i + 7);
    swap(this, i + 1, i + 6);
    swap(this, i + 2, i + 5);
    swap(this, i + 3, i + 4);
  }
  return this
};

Buffer$1.prototype.toString = function toString () {
  var length = this.length | 0;
  if (length === 0) return ''
  if (arguments.length === 0) return utf8Slice(this, 0, length)
  return slowToString.apply(this, arguments)
};

Buffer$1.prototype.equals = function equals (b) {
  if (!internalIsBuffer(b)) throw new TypeError('Argument must be a Buffer')
  if (this === b) return true
  return Buffer$1.compare(this, b) === 0
};

Buffer$1.prototype.inspect = function inspect () {
  var str = '';
  var max = INSPECT_MAX_BYTES;
  if (this.length > 0) {
    str = this.toString('hex', 0, max).match(/.{2}/g).join(' ');
    if (this.length > max) str += ' ... ';
  }
  return '<Buffer ' + str + '>'
};

Buffer$1.prototype.compare = function compare (target, start, end, thisStart, thisEnd) {
  if (!internalIsBuffer(target)) {
    throw new TypeError('Argument must be a Buffer')
  }

  if (start === undefined) {
    start = 0;
  }
  if (end === undefined) {
    end = target ? target.length : 0;
  }
  if (thisStart === undefined) {
    thisStart = 0;
  }
  if (thisEnd === undefined) {
    thisEnd = this.length;
  }

  if (start < 0 || end > target.length || thisStart < 0 || thisEnd > this.length) {
    throw new RangeError('out of range index')
  }

  if (thisStart >= thisEnd && start >= end) {
    return 0
  }
  if (thisStart >= thisEnd) {
    return -1
  }
  if (start >= end) {
    return 1
  }

  start >>>= 0;
  end >>>= 0;
  thisStart >>>= 0;
  thisEnd >>>= 0;

  if (this === target) return 0

  var x = thisEnd - thisStart;
  var y = end - start;
  var len = Math.min(x, y);

  var thisCopy = this.slice(thisStart, thisEnd);
  var targetCopy = target.slice(start, end);

  for (var i = 0; i < len; ++i) {
    if (thisCopy[i] !== targetCopy[i]) {
      x = thisCopy[i];
      y = targetCopy[i];
      break
    }
  }

  if (x < y) return -1
  if (y < x) return 1
  return 0
};

// Finds either the first index of `val` in `buffer` at offset >= `byteOffset`,
// OR the last index of `val` in `buffer` at offset <= `byteOffset`.
//
// Arguments:
// - buffer - a Buffer to search
// - val - a string, Buffer, or number
// - byteOffset - an index into `buffer`; will be clamped to an int32
// - encoding - an optional encoding, relevant is val is a string
// - dir - true for indexOf, false for lastIndexOf
function bidirectionalIndexOf (buffer, val, byteOffset, encoding, dir) {
  // Empty buffer means no match
  if (buffer.length === 0) return -1

  // Normalize byteOffset
  if (typeof byteOffset === 'string') {
    encoding = byteOffset;
    byteOffset = 0;
  } else if (byteOffset > 0x7fffffff) {
    byteOffset = 0x7fffffff;
  } else if (byteOffset < -0x80000000) {
    byteOffset = -0x80000000;
  }
  byteOffset = +byteOffset;  // Coerce to Number.
  if (isNaN(byteOffset)) {
    // byteOffset: it it's undefined, null, NaN, "foo", etc, search whole buffer
    byteOffset = dir ? 0 : (buffer.length - 1);
  }

  // Normalize byteOffset: negative offsets start from the end of the buffer
  if (byteOffset < 0) byteOffset = buffer.length + byteOffset;
  if (byteOffset >= buffer.length) {
    if (dir) return -1
    else byteOffset = buffer.length - 1;
  } else if (byteOffset < 0) {
    if (dir) byteOffset = 0;
    else return -1
  }

  // Normalize val
  if (typeof val === 'string') {
    val = Buffer$1.from(val, encoding);
  }

  // Finally, search either indexOf (if dir is true) or lastIndexOf
  if (internalIsBuffer(val)) {
    // Special case: looking for empty string/buffer always fails
    if (val.length === 0) {
      return -1
    }
    return arrayIndexOf(buffer, val, byteOffset, encoding, dir)
  } else if (typeof val === 'number') {
    val = val & 0xFF; // Search for a byte value [0-255]
    if (Buffer$1.TYPED_ARRAY_SUPPORT &&
        typeof Uint8Array.prototype.indexOf === 'function') {
      if (dir) {
        return Uint8Array.prototype.indexOf.call(buffer, val, byteOffset)
      } else {
        return Uint8Array.prototype.lastIndexOf.call(buffer, val, byteOffset)
      }
    }
    return arrayIndexOf(buffer, [ val ], byteOffset, encoding, dir)
  }

  throw new TypeError('val must be string, number or Buffer')
}

function arrayIndexOf (arr, val, byteOffset, encoding, dir) {
  var indexSize = 1;
  var arrLength = arr.length;
  var valLength = val.length;

  if (encoding !== undefined) {
    encoding = String(encoding).toLowerCase();
    if (encoding === 'ucs2' || encoding === 'ucs-2' ||
        encoding === 'utf16le' || encoding === 'utf-16le') {
      if (arr.length < 2 || val.length < 2) {
        return -1
      }
      indexSize = 2;
      arrLength /= 2;
      valLength /= 2;
      byteOffset /= 2;
    }
  }

  function read (buf, i) {
    if (indexSize === 1) {
      return buf[i]
    } else {
      return buf.readUInt16BE(i * indexSize)
    }
  }

  var i;
  if (dir) {
    var foundIndex = -1;
    for (i = byteOffset; i < arrLength; i++) {
      if (read(arr, i) === read(val, foundIndex === -1 ? 0 : i - foundIndex)) {
        if (foundIndex === -1) foundIndex = i;
        if (i - foundIndex + 1 === valLength) return foundIndex * indexSize
      } else {
        if (foundIndex !== -1) i -= i - foundIndex;
        foundIndex = -1;
      }
    }
  } else {
    if (byteOffset + valLength > arrLength) byteOffset = arrLength - valLength;
    for (i = byteOffset; i >= 0; i--) {
      var found = true;
      for (var j = 0; j < valLength; j++) {
        if (read(arr, i + j) !== read(val, j)) {
          found = false;
          break
        }
      }
      if (found) return i
    }
  }

  return -1
}

Buffer$1.prototype.includes = function includes (val, byteOffset, encoding) {
  return this.indexOf(val, byteOffset, encoding) !== -1
};

Buffer$1.prototype.indexOf = function indexOf (val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, true)
};

Buffer$1.prototype.lastIndexOf = function lastIndexOf (val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, false)
};

function hexWrite (buf, string, offset, length) {
  offset = Number(offset) || 0;
  var remaining = buf.length - offset;
  if (!length) {
    length = remaining;
  } else {
    length = Number(length);
    if (length > remaining) {
      length = remaining;
    }
  }

  // must be an even number of digits
  var strLen = string.length;
  if (strLen % 2 !== 0) throw new TypeError('Invalid hex string')

  if (length > strLen / 2) {
    length = strLen / 2;
  }
  for (var i = 0; i < length; ++i) {
    var parsed = parseInt(string.substr(i * 2, 2), 16);
    if (isNaN(parsed)) return i
    buf[offset + i] = parsed;
  }
  return i
}

function utf8Write (buf, string, offset, length) {
  return blitBuffer(utf8ToBytes(string, buf.length - offset), buf, offset, length)
}

function asciiWrite (buf, string, offset, length) {
  return blitBuffer(asciiToBytes(string), buf, offset, length)
}

function latin1Write (buf, string, offset, length) {
  return asciiWrite(buf, string, offset, length)
}

function base64Write (buf, string, offset, length) {
  return blitBuffer(base64ToBytes(string), buf, offset, length)
}

function ucs2Write (buf, string, offset, length) {
  return blitBuffer(utf16leToBytes(string, buf.length - offset), buf, offset, length)
}

Buffer$1.prototype.write = function write (string, offset, length, encoding) {
  // Buffer#write(string)
  if (offset === undefined) {
    encoding = 'utf8';
    length = this.length;
    offset = 0;
  // Buffer#write(string, encoding)
  } else if (length === undefined && typeof offset === 'string') {
    encoding = offset;
    length = this.length;
    offset = 0;
  // Buffer#write(string, offset[, length][, encoding])
  } else if (isFinite(offset)) {
    offset = offset | 0;
    if (isFinite(length)) {
      length = length | 0;
      if (encoding === undefined) encoding = 'utf8';
    } else {
      encoding = length;
      length = undefined;
    }
  // legacy write(string, encoding, offset, length) - remove in v0.13
  } else {
    throw new Error(
      'Buffer.write(string, encoding, offset[, length]) is no longer supported'
    )
  }

  var remaining = this.length - offset;
  if (length === undefined || length > remaining) length = remaining;

  if ((string.length > 0 && (length < 0 || offset < 0)) || offset > this.length) {
    throw new RangeError('Attempt to write outside buffer bounds')
  }

  if (!encoding) encoding = 'utf8';

  var loweredCase = false;
  for (;;) {
    switch (encoding) {
      case 'hex':
        return hexWrite(this, string, offset, length)

      case 'utf8':
      case 'utf-8':
        return utf8Write(this, string, offset, length)

      case 'ascii':
        return asciiWrite(this, string, offset, length)

      case 'latin1':
      case 'binary':
        return latin1Write(this, string, offset, length)

      case 'base64':
        // Warning: maxLength not taken into account in base64Write
        return base64Write(this, string, offset, length)

      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return ucs2Write(this, string, offset, length)

      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
        encoding = ('' + encoding).toLowerCase();
        loweredCase = true;
    }
  }
};

Buffer$1.prototype.toJSON = function toJSON () {
  return {
    type: 'Buffer',
    data: Array.prototype.slice.call(this._arr || this, 0)
  }
};

function base64Slice (buf, start, end) {
  if (start === 0 && end === buf.length) {
    return fromByteArray(buf)
  } else {
    return fromByteArray(buf.slice(start, end))
  }
}

function utf8Slice (buf, start, end) {
  end = Math.min(buf.length, end);
  var res = [];

  var i = start;
  while (i < end) {
    var firstByte = buf[i];
    var codePoint = null;
    var bytesPerSequence = (firstByte > 0xEF) ? 4
      : (firstByte > 0xDF) ? 3
      : (firstByte > 0xBF) ? 2
      : 1;

    if (i + bytesPerSequence <= end) {
      var secondByte, thirdByte, fourthByte, tempCodePoint;

      switch (bytesPerSequence) {
        case 1:
          if (firstByte < 0x80) {
            codePoint = firstByte;
          }
          break
        case 2:
          secondByte = buf[i + 1];
          if ((secondByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0x1F) << 0x6 | (secondByte & 0x3F);
            if (tempCodePoint > 0x7F) {
              codePoint = tempCodePoint;
            }
          }
          break
        case 3:
          secondByte = buf[i + 1];
          thirdByte = buf[i + 2];
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0xC | (secondByte & 0x3F) << 0x6 | (thirdByte & 0x3F);
            if (tempCodePoint > 0x7FF && (tempCodePoint < 0xD800 || tempCodePoint > 0xDFFF)) {
              codePoint = tempCodePoint;
            }
          }
          break
        case 4:
          secondByte = buf[i + 1];
          thirdByte = buf[i + 2];
          fourthByte = buf[i + 3];
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80 && (fourthByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0x12 | (secondByte & 0x3F) << 0xC | (thirdByte & 0x3F) << 0x6 | (fourthByte & 0x3F);
            if (tempCodePoint > 0xFFFF && tempCodePoint < 0x110000) {
              codePoint = tempCodePoint;
            }
          }
      }
    }

    if (codePoint === null) {
      // we did not generate a valid codePoint so insert a
      // replacement char (U+FFFD) and advance only 1 byte
      codePoint = 0xFFFD;
      bytesPerSequence = 1;
    } else if (codePoint > 0xFFFF) {
      // encode to utf16 (surrogate pair dance)
      codePoint -= 0x10000;
      res.push(codePoint >>> 10 & 0x3FF | 0xD800);
      codePoint = 0xDC00 | codePoint & 0x3FF;
    }

    res.push(codePoint);
    i += bytesPerSequence;
  }

  return decodeCodePointsArray(res)
}

// Based on http://stackoverflow.com/a/22747272/680742, the browser with
// the lowest limit is Chrome, with 0x10000 args.
// We go 1 magnitude less, for safety
var MAX_ARGUMENTS_LENGTH = 0x1000;

function decodeCodePointsArray (codePoints) {
  var len = codePoints.length;
  if (len <= MAX_ARGUMENTS_LENGTH) {
    return String.fromCharCode.apply(String, codePoints) // avoid extra slice()
  }

  // Decode in chunks to avoid "call stack size exceeded".
  var res = '';
  var i = 0;
  while (i < len) {
    res += String.fromCharCode.apply(
      String,
      codePoints.slice(i, i += MAX_ARGUMENTS_LENGTH)
    );
  }
  return res
}

function asciiSlice (buf, start, end) {
  var ret = '';
  end = Math.min(buf.length, end);

  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i] & 0x7F);
  }
  return ret
}

function latin1Slice (buf, start, end) {
  var ret = '';
  end = Math.min(buf.length, end);

  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i]);
  }
  return ret
}

function hexSlice (buf, start, end) {
  var len = buf.length;

  if (!start || start < 0) start = 0;
  if (!end || end < 0 || end > len) end = len;

  var out = '';
  for (var i = start; i < end; ++i) {
    out += toHex(buf[i]);
  }
  return out
}

function utf16leSlice (buf, start, end) {
  var bytes = buf.slice(start, end);
  var res = '';
  for (var i = 0; i < bytes.length; i += 2) {
    res += String.fromCharCode(bytes[i] + bytes[i + 1] * 256);
  }
  return res
}

Buffer$1.prototype.slice = function slice (start, end) {
  var len = this.length;
  start = ~~start;
  end = end === undefined ? len : ~~end;

  if (start < 0) {
    start += len;
    if (start < 0) start = 0;
  } else if (start > len) {
    start = len;
  }

  if (end < 0) {
    end += len;
    if (end < 0) end = 0;
  } else if (end > len) {
    end = len;
  }

  if (end < start) end = start;

  var newBuf;
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    newBuf = this.subarray(start, end);
    newBuf.__proto__ = Buffer$1.prototype;
  } else {
    var sliceLen = end - start;
    newBuf = new Buffer$1(sliceLen, undefined);
    for (var i = 0; i < sliceLen; ++i) {
      newBuf[i] = this[i + start];
    }
  }

  return newBuf
};

/*
 * Need to make sure that buffer isn't trying to write out of bounds.
 */
function checkOffset (offset, ext, length) {
  if ((offset % 1) !== 0 || offset < 0) throw new RangeError('offset is not uint')
  if (offset + ext > length) throw new RangeError('Trying to access beyond buffer length')
}

Buffer$1.prototype.readUIntLE = function readUIntLE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);

  var val = this[offset];
  var mul = 1;
  var i = 0;
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul;
  }

  return val
};

Buffer$1.prototype.readUIntBE = function readUIntBE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) {
    checkOffset(offset, byteLength, this.length);
  }

  var val = this[offset + --byteLength];
  var mul = 1;
  while (byteLength > 0 && (mul *= 0x100)) {
    val += this[offset + --byteLength] * mul;
  }

  return val
};

Buffer$1.prototype.readUInt8 = function readUInt8 (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 1, this.length);
  return this[offset]
};

Buffer$1.prototype.readUInt16LE = function readUInt16LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  return this[offset] | (this[offset + 1] << 8)
};

Buffer$1.prototype.readUInt16BE = function readUInt16BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  return (this[offset] << 8) | this[offset + 1]
};

Buffer$1.prototype.readUInt32LE = function readUInt32LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return ((this[offset]) |
      (this[offset + 1] << 8) |
      (this[offset + 2] << 16)) +
      (this[offset + 3] * 0x1000000)
};

Buffer$1.prototype.readUInt32BE = function readUInt32BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return (this[offset] * 0x1000000) +
    ((this[offset + 1] << 16) |
    (this[offset + 2] << 8) |
    this[offset + 3])
};

Buffer$1.prototype.readIntLE = function readIntLE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);

  var val = this[offset];
  var mul = 1;
  var i = 0;
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul;
  }
  mul *= 0x80;

  if (val >= mul) val -= Math.pow(2, 8 * byteLength);

  return val
};

Buffer$1.prototype.readIntBE = function readIntBE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);

  var i = byteLength;
  var mul = 1;
  var val = this[offset + --i];
  while (i > 0 && (mul *= 0x100)) {
    val += this[offset + --i] * mul;
  }
  mul *= 0x80;

  if (val >= mul) val -= Math.pow(2, 8 * byteLength);

  return val
};

Buffer$1.prototype.readInt8 = function readInt8 (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 1, this.length);
  if (!(this[offset] & 0x80)) return (this[offset])
  return ((0xff - this[offset] + 1) * -1)
};

Buffer$1.prototype.readInt16LE = function readInt16LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  var val = this[offset] | (this[offset + 1] << 8);
  return (val & 0x8000) ? val | 0xFFFF0000 : val
};

Buffer$1.prototype.readInt16BE = function readInt16BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  var val = this[offset + 1] | (this[offset] << 8);
  return (val & 0x8000) ? val | 0xFFFF0000 : val
};

Buffer$1.prototype.readInt32LE = function readInt32LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return (this[offset]) |
    (this[offset + 1] << 8) |
    (this[offset + 2] << 16) |
    (this[offset + 3] << 24)
};

Buffer$1.prototype.readInt32BE = function readInt32BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return (this[offset] << 24) |
    (this[offset + 1] << 16) |
    (this[offset + 2] << 8) |
    (this[offset + 3])
};

Buffer$1.prototype.readFloatLE = function readFloatLE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);
  return read(this, offset, true, 23, 4)
};

Buffer$1.prototype.readFloatBE = function readFloatBE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);
  return read(this, offset, false, 23, 4)
};

Buffer$1.prototype.readDoubleLE = function readDoubleLE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 8, this.length);
  return read(this, offset, true, 52, 8)
};

Buffer$1.prototype.readDoubleBE = function readDoubleBE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 8, this.length);
  return read(this, offset, false, 52, 8)
};

function checkInt (buf, value, offset, ext, max, min) {
  if (!internalIsBuffer(buf)) throw new TypeError('"buffer" argument must be a Buffer instance')
  if (value > max || value < min) throw new RangeError('"value" argument is out of bounds')
  if (offset + ext > buf.length) throw new RangeError('Index out of range')
}

Buffer$1.prototype.writeUIntLE = function writeUIntLE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1;
    checkInt(this, value, offset, byteLength, maxBytes, 0);
  }

  var mul = 1;
  var i = 0;
  this[offset] = value & 0xFF;
  while (++i < byteLength && (mul *= 0x100)) {
    this[offset + i] = (value / mul) & 0xFF;
  }

  return offset + byteLength
};

Buffer$1.prototype.writeUIntBE = function writeUIntBE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1;
    checkInt(this, value, offset, byteLength, maxBytes, 0);
  }

  var i = byteLength - 1;
  var mul = 1;
  this[offset + i] = value & 0xFF;
  while (--i >= 0 && (mul *= 0x100)) {
    this[offset + i] = (value / mul) & 0xFF;
  }

  return offset + byteLength
};

Buffer$1.prototype.writeUInt8 = function writeUInt8 (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 1, 0xff, 0);
  if (!Buffer$1.TYPED_ARRAY_SUPPORT) value = Math.floor(value);
  this[offset] = (value & 0xff);
  return offset + 1
};

function objectWriteUInt16 (buf, value, offset, littleEndian) {
  if (value < 0) value = 0xffff + value + 1;
  for (var i = 0, j = Math.min(buf.length - offset, 2); i < j; ++i) {
    buf[offset + i] = (value & (0xff << (8 * (littleEndian ? i : 1 - i)))) >>>
      (littleEndian ? i : 1 - i) * 8;
  }
}

Buffer$1.prototype.writeUInt16LE = function writeUInt16LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff);
    this[offset + 1] = (value >>> 8);
  } else {
    objectWriteUInt16(this, value, offset, true);
  }
  return offset + 2
};

Buffer$1.prototype.writeUInt16BE = function writeUInt16BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 8);
    this[offset + 1] = (value & 0xff);
  } else {
    objectWriteUInt16(this, value, offset, false);
  }
  return offset + 2
};

function objectWriteUInt32 (buf, value, offset, littleEndian) {
  if (value < 0) value = 0xffffffff + value + 1;
  for (var i = 0, j = Math.min(buf.length - offset, 4); i < j; ++i) {
    buf[offset + i] = (value >>> (littleEndian ? i : 3 - i) * 8) & 0xff;
  }
}

Buffer$1.prototype.writeUInt32LE = function writeUInt32LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset + 3] = (value >>> 24);
    this[offset + 2] = (value >>> 16);
    this[offset + 1] = (value >>> 8);
    this[offset] = (value & 0xff);
  } else {
    objectWriteUInt32(this, value, offset, true);
  }
  return offset + 4
};

Buffer$1.prototype.writeUInt32BE = function writeUInt32BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 24);
    this[offset + 1] = (value >>> 16);
    this[offset + 2] = (value >>> 8);
    this[offset + 3] = (value & 0xff);
  } else {
    objectWriteUInt32(this, value, offset, false);
  }
  return offset + 4
};

Buffer$1.prototype.writeIntLE = function writeIntLE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1);

    checkInt(this, value, offset, byteLength, limit - 1, -limit);
  }

  var i = 0;
  var mul = 1;
  var sub = 0;
  this[offset] = value & 0xFF;
  while (++i < byteLength && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i - 1] !== 0) {
      sub = 1;
    }
    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF;
  }

  return offset + byteLength
};

Buffer$1.prototype.writeIntBE = function writeIntBE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1);

    checkInt(this, value, offset, byteLength, limit - 1, -limit);
  }

  var i = byteLength - 1;
  var mul = 1;
  var sub = 0;
  this[offset + i] = value & 0xFF;
  while (--i >= 0 && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i + 1] !== 0) {
      sub = 1;
    }
    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF;
  }

  return offset + byteLength
};

Buffer$1.prototype.writeInt8 = function writeInt8 (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 1, 0x7f, -0x80);
  if (!Buffer$1.TYPED_ARRAY_SUPPORT) value = Math.floor(value);
  if (value < 0) value = 0xff + value + 1;
  this[offset] = (value & 0xff);
  return offset + 1
};

Buffer$1.prototype.writeInt16LE = function writeInt16LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff);
    this[offset + 1] = (value >>> 8);
  } else {
    objectWriteUInt16(this, value, offset, true);
  }
  return offset + 2
};

Buffer$1.prototype.writeInt16BE = function writeInt16BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 8);
    this[offset + 1] = (value & 0xff);
  } else {
    objectWriteUInt16(this, value, offset, false);
  }
  return offset + 2
};

Buffer$1.prototype.writeInt32LE = function writeInt32LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000);
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff);
    this[offset + 1] = (value >>> 8);
    this[offset + 2] = (value >>> 16);
    this[offset + 3] = (value >>> 24);
  } else {
    objectWriteUInt32(this, value, offset, true);
  }
  return offset + 4
};

Buffer$1.prototype.writeInt32BE = function writeInt32BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000);
  if (value < 0) value = 0xffffffff + value + 1;
  if (Buffer$1.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 24);
    this[offset + 1] = (value >>> 16);
    this[offset + 2] = (value >>> 8);
    this[offset + 3] = (value & 0xff);
  } else {
    objectWriteUInt32(this, value, offset, false);
  }
  return offset + 4
};

function checkIEEE754 (buf, value, offset, ext, max, min) {
  if (offset + ext > buf.length) throw new RangeError('Index out of range')
  if (offset < 0) throw new RangeError('Index out of range')
}

function writeFloat (buf, value, offset, littleEndian, noAssert) {
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 4);
  }
  write(buf, value, offset, littleEndian, 23, 4);
  return offset + 4
}

Buffer$1.prototype.writeFloatLE = function writeFloatLE (value, offset, noAssert) {
  return writeFloat(this, value, offset, true, noAssert)
};

Buffer$1.prototype.writeFloatBE = function writeFloatBE (value, offset, noAssert) {
  return writeFloat(this, value, offset, false, noAssert)
};

function writeDouble (buf, value, offset, littleEndian, noAssert) {
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 8);
  }
  write(buf, value, offset, littleEndian, 52, 8);
  return offset + 8
}

Buffer$1.prototype.writeDoubleLE = function writeDoubleLE (value, offset, noAssert) {
  return writeDouble(this, value, offset, true, noAssert)
};

Buffer$1.prototype.writeDoubleBE = function writeDoubleBE (value, offset, noAssert) {
  return writeDouble(this, value, offset, false, noAssert)
};

// copy(targetBuffer, targetStart=0, sourceStart=0, sourceEnd=buffer.length)
Buffer$1.prototype.copy = function copy (target, targetStart, start, end) {
  if (!start) start = 0;
  if (!end && end !== 0) end = this.length;
  if (targetStart >= target.length) targetStart = target.length;
  if (!targetStart) targetStart = 0;
  if (end > 0 && end < start) end = start;

  // Copy 0 bytes; we're done
  if (end === start) return 0
  if (target.length === 0 || this.length === 0) return 0

  // Fatal error conditions
  if (targetStart < 0) {
    throw new RangeError('targetStart out of bounds')
  }
  if (start < 0 || start >= this.length) throw new RangeError('sourceStart out of bounds')
  if (end < 0) throw new RangeError('sourceEnd out of bounds')

  // Are we oob?
  if (end > this.length) end = this.length;
  if (target.length - targetStart < end - start) {
    end = target.length - targetStart + start;
  }

  var len = end - start;
  var i;

  if (this === target && start < targetStart && targetStart < end) {
    // descending copy from end
    for (i = len - 1; i >= 0; --i) {
      target[i + targetStart] = this[i + start];
    }
  } else if (len < 1000 || !Buffer$1.TYPED_ARRAY_SUPPORT) {
    // ascending copy from start
    for (i = 0; i < len; ++i) {
      target[i + targetStart] = this[i + start];
    }
  } else {
    Uint8Array.prototype.set.call(
      target,
      this.subarray(start, start + len),
      targetStart
    );
  }

  return len
};

// Usage:
//    buffer.fill(number[, offset[, end]])
//    buffer.fill(buffer[, offset[, end]])
//    buffer.fill(string[, offset[, end]][, encoding])
Buffer$1.prototype.fill = function fill (val, start, end, encoding) {
  // Handle string cases:
  if (typeof val === 'string') {
    if (typeof start === 'string') {
      encoding = start;
      start = 0;
      end = this.length;
    } else if (typeof end === 'string') {
      encoding = end;
      end = this.length;
    }
    if (val.length === 1) {
      var code = val.charCodeAt(0);
      if (code < 256) {
        val = code;
      }
    }
    if (encoding !== undefined && typeof encoding !== 'string') {
      throw new TypeError('encoding must be a string')
    }
    if (typeof encoding === 'string' && !Buffer$1.isEncoding(encoding)) {
      throw new TypeError('Unknown encoding: ' + encoding)
    }
  } else if (typeof val === 'number') {
    val = val & 255;
  }

  // Invalid ranges are not set to a default, so can range check early.
  if (start < 0 || this.length < start || this.length < end) {
    throw new RangeError('Out of range index')
  }

  if (end <= start) {
    return this
  }

  start = start >>> 0;
  end = end === undefined ? this.length : end >>> 0;

  if (!val) val = 0;

  var i;
  if (typeof val === 'number') {
    for (i = start; i < end; ++i) {
      this[i] = val;
    }
  } else {
    var bytes = internalIsBuffer(val)
      ? val
      : utf8ToBytes(new Buffer$1(val, encoding).toString());
    var len = bytes.length;
    for (i = 0; i < end - start; ++i) {
      this[i + start] = bytes[i % len];
    }
  }

  return this
};

// HELPER FUNCTIONS
// ================

var INVALID_BASE64_RE = /[^+\/0-9A-Za-z-_]/g;

function base64clean (str) {
  // Node strips out invalid characters like \n and \t from the string, base64-js does not
  str = stringtrim(str).replace(INVALID_BASE64_RE, '');
  // Node converts strings with length < 2 to ''
  if (str.length < 2) return ''
  // Node allows for non-padded base64 strings (missing trailing ===), base64-js does not
  while (str.length % 4 !== 0) {
    str = str + '=';
  }
  return str
}

function stringtrim (str) {
  if (str.trim) return str.trim()
  return str.replace(/^\s+|\s+$/g, '')
}

function toHex (n) {
  if (n < 16) return '0' + n.toString(16)
  return n.toString(16)
}

function utf8ToBytes (string, units) {
  units = units || Infinity;
  var codePoint;
  var length = string.length;
  var leadSurrogate = null;
  var bytes = [];

  for (var i = 0; i < length; ++i) {
    codePoint = string.charCodeAt(i);

    // is surrogate component
    if (codePoint > 0xD7FF && codePoint < 0xE000) {
      // last char was a lead
      if (!leadSurrogate) {
        // no lead yet
        if (codePoint > 0xDBFF) {
          // unexpected trail
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
          continue
        } else if (i + 1 === length) {
          // unpaired lead
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
          continue
        }

        // valid lead
        leadSurrogate = codePoint;

        continue
      }

      // 2 leads in a row
      if (codePoint < 0xDC00) {
        if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
        leadSurrogate = codePoint;
        continue
      }

      // valid surrogate pair
      codePoint = (leadSurrogate - 0xD800 << 10 | codePoint - 0xDC00) + 0x10000;
    } else if (leadSurrogate) {
      // valid bmp char, but last char was a lead
      if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
    }

    leadSurrogate = null;

    // encode utf8
    if (codePoint < 0x80) {
      if ((units -= 1) < 0) break
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      if ((units -= 2) < 0) break
      bytes.push(
        codePoint >> 0x6 | 0xC0,
        codePoint & 0x3F | 0x80
      );
    } else if (codePoint < 0x10000) {
      if ((units -= 3) < 0) break
      bytes.push(
        codePoint >> 0xC | 0xE0,
        codePoint >> 0x6 & 0x3F | 0x80,
        codePoint & 0x3F | 0x80
      );
    } else if (codePoint < 0x110000) {
      if ((units -= 4) < 0) break
      bytes.push(
        codePoint >> 0x12 | 0xF0,
        codePoint >> 0xC & 0x3F | 0x80,
        codePoint >> 0x6 & 0x3F | 0x80,
        codePoint & 0x3F | 0x80
      );
    } else {
      throw new Error('Invalid code point')
    }
  }

  return bytes
}

function asciiToBytes (str) {
  var byteArray = [];
  for (var i = 0; i < str.length; ++i) {
    // Node's code seems to be doing this and not & 0x7F..
    byteArray.push(str.charCodeAt(i) & 0xFF);
  }
  return byteArray
}

function utf16leToBytes (str, units) {
  var c, hi, lo;
  var byteArray = [];
  for (var i = 0; i < str.length; ++i) {
    if ((units -= 2) < 0) break

    c = str.charCodeAt(i);
    hi = c >> 8;
    lo = c % 256;
    byteArray.push(lo);
    byteArray.push(hi);
  }

  return byteArray
}


function base64ToBytes (str) {
  return toByteArray(base64clean(str))
}

function blitBuffer (src, dst, offset, length) {
  for (var i = 0; i < length; ++i) {
    if ((i + offset >= dst.length) || (i >= src.length)) break
    dst[i + offset] = src[i];
  }
  return i
}

function isnan (val) {
  return val !== val // eslint-disable-line no-self-compare
}


// the following is from is-buffer, also by Feross Aboukhadijeh and with same lisence
// The _isBuffer check is for Safari 5-7 support, because it's missing
// Object.prototype.constructor. Remove this eventually
function isBuffer(obj) {
  return obj != null && (!!obj._isBuffer || isFastBuffer(obj) || isSlowBuffer(obj))
}

function isFastBuffer (obj) {
  return !!obj.constructor && typeof obj.constructor.isBuffer === 'function' && obj.constructor.isBuffer(obj)
}

// For Node v0.10 support. Remove this eventually.
function isSlowBuffer (obj) {
  return typeof obj.readFloatLE === 'function' && typeof obj.slice === 'function' && isFastBuffer(obj.slice(0, 0))
}

function BufferList() {
  this.head = null;
  this.tail = null;
  this.length = 0;
}

BufferList.prototype.push = function (v) {
  var entry = { data: v, next: null };
  if (this.length > 0) this.tail.next = entry;else this.head = entry;
  this.tail = entry;
  ++this.length;
};

BufferList.prototype.unshift = function (v) {
  var entry = { data: v, next: this.head };
  if (this.length === 0) this.tail = entry;
  this.head = entry;
  ++this.length;
};

BufferList.prototype.shift = function () {
  if (this.length === 0) return;
  var ret = this.head.data;
  if (this.length === 1) this.head = this.tail = null;else this.head = this.head.next;
  --this.length;
  return ret;
};

BufferList.prototype.clear = function () {
  this.head = this.tail = null;
  this.length = 0;
};

BufferList.prototype.join = function (s) {
  if (this.length === 0) return '';
  var p = this.head;
  var ret = '' + p.data;
  while (p = p.next) {
    ret += s + p.data;
  }return ret;
};

BufferList.prototype.concat = function (n) {
  if (this.length === 0) return Buffer$1.alloc(0);
  if (this.length === 1) return this.head.data;
  var ret = Buffer$1.allocUnsafe(n >>> 0);
  var p = this.head;
  var i = 0;
  while (p) {
    p.data.copy(ret, i);
    i += p.data.length;
    p = p.next;
  }
  return ret;
};

// Copyright Joyent, Inc. and other Node contributors.
var isBufferEncoding = Buffer$1.isEncoding
  || function(encoding) {
       switch (encoding && encoding.toLowerCase()) {
         case 'hex': case 'utf8': case 'utf-8': case 'ascii': case 'binary': case 'base64': case 'ucs2': case 'ucs-2': case 'utf16le': case 'utf-16le': case 'raw': return true;
         default: return false;
       }
     };


function assertEncoding(encoding) {
  if (encoding && !isBufferEncoding(encoding)) {
    throw new Error('Unknown encoding: ' + encoding);
  }
}

// StringDecoder provides an interface for efficiently splitting a series of
// buffers into a series of JS strings without breaking apart multi-byte
// characters. CESU-8 is handled as part of the UTF-8 encoding.
//
// @TODO Handling all encodings inside a single object makes it very difficult
// to reason about this code, so it should be split up in the future.
// @TODO There should be a utf8-strict encoding that rejects invalid UTF-8 code
// points as used by CESU-8.
function StringDecoder(encoding) {
  this.encoding = (encoding || 'utf8').toLowerCase().replace(/[-_]/, '');
  assertEncoding(encoding);
  switch (this.encoding) {
    case 'utf8':
      // CESU-8 represents each of Surrogate Pair by 3-bytes
      this.surrogateSize = 3;
      break;
    case 'ucs2':
    case 'utf16le':
      // UTF-16 represents each of Surrogate Pair by 2-bytes
      this.surrogateSize = 2;
      this.detectIncompleteChar = utf16DetectIncompleteChar;
      break;
    case 'base64':
      // Base-64 stores 3 bytes in 4 chars, and pads the remainder.
      this.surrogateSize = 3;
      this.detectIncompleteChar = base64DetectIncompleteChar;
      break;
    default:
      this.write = passThroughWrite;
      return;
  }

  // Enough space to store all bytes of a single character. UTF-8 needs 4
  // bytes, but CESU-8 may require up to 6 (3 bytes per surrogate).
  this.charBuffer = new Buffer$1(6);
  // Number of bytes received for the current incomplete multi-byte character.
  this.charReceived = 0;
  // Number of bytes expected for the current incomplete multi-byte character.
  this.charLength = 0;
}

// write decodes the given buffer and returns it as JS string that is
// guaranteed to not contain any partial multi-byte characters. Any partial
// character found at the end of the buffer is buffered up, and will be
// returned when calling write again with the remaining bytes.
//
// Note: Converting a Buffer containing an orphan surrogate to a String
// currently works, but converting a String to a Buffer (via `new Buffer`, or
// Buffer#write) will replace incomplete surrogates with the unicode
// replacement character. See https://codereview.chromium.org/121173009/ .
StringDecoder.prototype.write = function(buffer) {
  var charStr = '';
  // if our last write ended with an incomplete multibyte character
  while (this.charLength) {
    // determine how many remaining bytes this buffer has to offer for this char
    var available = (buffer.length >= this.charLength - this.charReceived) ?
        this.charLength - this.charReceived :
        buffer.length;

    // add the new bytes to the char buffer
    buffer.copy(this.charBuffer, this.charReceived, 0, available);
    this.charReceived += available;

    if (this.charReceived < this.charLength) {
      // still not enough chars in this buffer? wait for more ...
      return '';
    }

    // remove bytes belonging to the current character from the buffer
    buffer = buffer.slice(available, buffer.length);

    // get the character that was split
    charStr = this.charBuffer.slice(0, this.charLength).toString(this.encoding);

    // CESU-8: lead surrogate (D800-DBFF) is also the incomplete character
    var charCode = charStr.charCodeAt(charStr.length - 1);
    if (charCode >= 0xD800 && charCode <= 0xDBFF) {
      this.charLength += this.surrogateSize;
      charStr = '';
      continue;
    }
    this.charReceived = this.charLength = 0;

    // if there are no more bytes in this buffer, just emit our char
    if (buffer.length === 0) {
      return charStr;
    }
    break;
  }

  // determine and set charLength / charReceived
  this.detectIncompleteChar(buffer);

  var end = buffer.length;
  if (this.charLength) {
    // buffer the incomplete character bytes we got
    buffer.copy(this.charBuffer, 0, buffer.length - this.charReceived, end);
    end -= this.charReceived;
  }

  charStr += buffer.toString(this.encoding, 0, end);

  var end = charStr.length - 1;
  var charCode = charStr.charCodeAt(end);
  // CESU-8: lead surrogate (D800-DBFF) is also the incomplete character
  if (charCode >= 0xD800 && charCode <= 0xDBFF) {
    var size = this.surrogateSize;
    this.charLength += size;
    this.charReceived += size;
    this.charBuffer.copy(this.charBuffer, size, 0, size);
    buffer.copy(this.charBuffer, 0, 0, size);
    return charStr.substring(0, end);
  }

  // or just emit the charStr
  return charStr;
};

// detectIncompleteChar determines if there is an incomplete UTF-8 character at
// the end of the given buffer. If so, it sets this.charLength to the byte
// length that character, and sets this.charReceived to the number of bytes
// that are available for this character.
StringDecoder.prototype.detectIncompleteChar = function(buffer) {
  // determine how many bytes we have to check at the end of this buffer
  var i = (buffer.length >= 3) ? 3 : buffer.length;

  // Figure out if one of the last i bytes of our buffer announces an
  // incomplete char.
  for (; i > 0; i--) {
    var c = buffer[buffer.length - i];

    // See http://en.wikipedia.org/wiki/UTF-8#Description

    // 110XXXXX
    if (i == 1 && c >> 5 == 0x06) {
      this.charLength = 2;
      break;
    }

    // 1110XXXX
    if (i <= 2 && c >> 4 == 0x0E) {
      this.charLength = 3;
      break;
    }

    // 11110XXX
    if (i <= 3 && c >> 3 == 0x1E) {
      this.charLength = 4;
      break;
    }
  }
  this.charReceived = i;
};

StringDecoder.prototype.end = function(buffer) {
  var res = '';
  if (buffer && buffer.length)
    res = this.write(buffer);

  if (this.charReceived) {
    var cr = this.charReceived;
    var buf = this.charBuffer;
    var enc = this.encoding;
    res += buf.slice(0, cr).toString(enc);
  }

  return res;
};

function passThroughWrite(buffer) {
  return buffer.toString(this.encoding);
}

function utf16DetectIncompleteChar(buffer) {
  this.charReceived = buffer.length % 2;
  this.charLength = this.charReceived ? 2 : 0;
}

function base64DetectIncompleteChar(buffer) {
  this.charReceived = buffer.length % 3;
  this.charLength = this.charReceived ? 3 : 0;
}

var stringDecoder = /*#__PURE__*/Object.freeze({
  __proto__: null,
  StringDecoder: StringDecoder
});

Readable.ReadableState = ReadableState;

var debug = debuglog('stream');
inherits$1(Readable, EventEmitter);

function prependListener(emitter, event, fn) {
  // Sadly this is not cacheable as some libraries bundle their own
  // event emitter implementation with them.
  if (typeof emitter.prependListener === 'function') {
    return emitter.prependListener(event, fn);
  } else {
    // This is a hack to make sure that our error handler is attached before any
    // userland ones.  NEVER DO THIS. This is here only because this code needs
    // to continue to work with older versions of Node.js that do not include
    // the prependListener() method. The goal is to eventually remove this hack.
    if (!emitter._events || !emitter._events[event])
      emitter.on(event, fn);
    else if (Array.isArray(emitter._events[event]))
      emitter._events[event].unshift(fn);
    else
      emitter._events[event] = [fn, emitter._events[event]];
  }
}
function listenerCount (emitter, type) {
  return emitter.listeners(type).length;
}
function ReadableState(options, stream) {

  options = options || {};

  // object stream flag. Used to make read(n) ignore n and to
  // make all the buffer merging and length checks go away
  this.objectMode = !!options.objectMode;

  if (stream instanceof Duplex) this.objectMode = this.objectMode || !!options.readableObjectMode;

  // the point at which it stops calling _read() to fill the buffer
  // Note: 0 is a valid value, means "don't call _read preemptively ever"
  var hwm = options.highWaterMark;
  var defaultHwm = this.objectMode ? 16 : 16 * 1024;
  this.highWaterMark = hwm || hwm === 0 ? hwm : defaultHwm;

  // cast to ints.
  this.highWaterMark = ~ ~this.highWaterMark;

  // A linked list is used to store data chunks instead of an array because the
  // linked list can remove elements from the beginning faster than
  // array.shift()
  this.buffer = new BufferList();
  this.length = 0;
  this.pipes = null;
  this.pipesCount = 0;
  this.flowing = null;
  this.ended = false;
  this.endEmitted = false;
  this.reading = false;

  // a flag to be able to tell if the onwrite cb is called immediately,
  // or on a later tick.  We set this to true at first, because any
  // actions that shouldn't happen until "later" should generally also
  // not happen before the first write call.
  this.sync = true;

  // whenever we return null, then we set a flag to say
  // that we're awaiting a 'readable' event emission.
  this.needReadable = false;
  this.emittedReadable = false;
  this.readableListening = false;
  this.resumeScheduled = false;

  // Crypto is kind of old and crusty.  Historically, its default string
  // encoding is 'binary' so we have to make this configurable.
  // Everything else in the universe uses 'utf8', though.
  this.defaultEncoding = options.defaultEncoding || 'utf8';

  // when piping, we only care about 'readable' events that happen
  // after read()ing all the bytes and not getting any pushback.
  this.ranOut = false;

  // the number of writers that are awaiting a drain event in .pipe()s
  this.awaitDrain = 0;

  // if true, a maybeReadMore has been scheduled
  this.readingMore = false;

  this.decoder = null;
  this.encoding = null;
  if (options.encoding) {
    this.decoder = new StringDecoder(options.encoding);
    this.encoding = options.encoding;
  }
}
function Readable(options) {

  if (!(this instanceof Readable)) return new Readable(options);

  this._readableState = new ReadableState(options, this);

  // legacy
  this.readable = true;

  if (options && typeof options.read === 'function') this._read = options.read;

  EventEmitter.call(this);
}

// Manually shove something into the read() buffer.
// This returns true if the highWaterMark has not been hit yet,
// similar to how Writable.write() returns true if you should
// write() some more.
Readable.prototype.push = function (chunk, encoding) {
  var state = this._readableState;

  if (!state.objectMode && typeof chunk === 'string') {
    encoding = encoding || state.defaultEncoding;
    if (encoding !== state.encoding) {
      chunk = Buffer.from(chunk, encoding);
      encoding = '';
    }
  }

  return readableAddChunk(this, state, chunk, encoding, false);
};

// Unshift should *always* be something directly out of read()
Readable.prototype.unshift = function (chunk) {
  var state = this._readableState;
  return readableAddChunk(this, state, chunk, '', true);
};

Readable.prototype.isPaused = function () {
  return this._readableState.flowing === false;
};

function readableAddChunk(stream, state, chunk, encoding, addToFront) {
  var er = chunkInvalid(state, chunk);
  if (er) {
    stream.emit('error', er);
  } else if (chunk === null) {
    state.reading = false;
    onEofChunk(stream, state);
  } else if (state.objectMode || chunk && chunk.length > 0) {
    if (state.ended && !addToFront) {
      var e = new Error('stream.push() after EOF');
      stream.emit('error', e);
    } else if (state.endEmitted && addToFront) {
      var _e = new Error('stream.unshift() after end event');
      stream.emit('error', _e);
    } else {
      var skipAdd;
      if (state.decoder && !addToFront && !encoding) {
        chunk = state.decoder.write(chunk);
        skipAdd = !state.objectMode && chunk.length === 0;
      }

      if (!addToFront) state.reading = false;

      // Don't add to the buffer if we've decoded to an empty string chunk and
      // we're not in object mode
      if (!skipAdd) {
        // if we want the data now, just emit it.
        if (state.flowing && state.length === 0 && !state.sync) {
          stream.emit('data', chunk);
          stream.read(0);
        } else {
          // update the buffer info.
          state.length += state.objectMode ? 1 : chunk.length;
          if (addToFront) state.buffer.unshift(chunk);else state.buffer.push(chunk);

          if (state.needReadable) emitReadable(stream);
        }
      }

      maybeReadMore(stream, state);
    }
  } else if (!addToFront) {
    state.reading = false;
  }

  return needMoreData(state);
}

// if it's past the high water mark, we can push in some more.
// Also, if we have no data yet, we can stand some
// more bytes.  This is to work around cases where hwm=0,
// such as the repl.  Also, if the push() triggered a
// readable event, and the user called read(largeNumber) such that
// needReadable was set, then we ought to push more, so that another
// 'readable' event will be triggered.
function needMoreData(state) {
  return !state.ended && (state.needReadable || state.length < state.highWaterMark || state.length === 0);
}

// backwards compatibility.
Readable.prototype.setEncoding = function (enc) {
  this._readableState.decoder = new StringDecoder(enc);
  this._readableState.encoding = enc;
  return this;
};

// Don't raise the hwm > 8MB
var MAX_HWM = 0x800000;
function computeNewHighWaterMark(n) {
  if (n >= MAX_HWM) {
    n = MAX_HWM;
  } else {
    // Get the next highest power of 2 to prevent increasing hwm excessively in
    // tiny amounts
    n--;
    n |= n >>> 1;
    n |= n >>> 2;
    n |= n >>> 4;
    n |= n >>> 8;
    n |= n >>> 16;
    n++;
  }
  return n;
}

// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function howMuchToRead(n, state) {
  if (n <= 0 || state.length === 0 && state.ended) return 0;
  if (state.objectMode) return 1;
  if (n !== n) {
    // Only flow one buffer at a time
    if (state.flowing && state.length) return state.buffer.head.data.length;else return state.length;
  }
  // If we're asking for more than the current hwm, then raise the hwm.
  if (n > state.highWaterMark) state.highWaterMark = computeNewHighWaterMark(n);
  if (n <= state.length) return n;
  // Don't have enough
  if (!state.ended) {
    state.needReadable = true;
    return 0;
  }
  return state.length;
}

// you can override either this method, or the async _read(n) below.
Readable.prototype.read = function (n) {
  debug('read', n);
  n = parseInt(n, 10);
  var state = this._readableState;
  var nOrig = n;

  if (n !== 0) state.emittedReadable = false;

  // if we're doing read(0) to trigger a readable event, but we
  // already have a bunch of data in the buffer, then just trigger
  // the 'readable' event and move on.
  if (n === 0 && state.needReadable && (state.length >= state.highWaterMark || state.ended)) {
    debug('read: emitReadable', state.length, state.ended);
    if (state.length === 0 && state.ended) endReadable(this);else emitReadable(this);
    return null;
  }

  n = howMuchToRead(n, state);

  // if we've ended, and we're now clear, then finish it up.
  if (n === 0 && state.ended) {
    if (state.length === 0) endReadable(this);
    return null;
  }

  // All the actual chunk generation logic needs to be
  // *below* the call to _read.  The reason is that in certain
  // synthetic stream cases, such as passthrough streams, _read
  // may be a completely synchronous operation which may change
  // the state of the read buffer, providing enough data when
  // before there was *not* enough.
  //
  // So, the steps are:
  // 1. Figure out what the state of things will be after we do
  // a read from the buffer.
  //
  // 2. If that resulting state will trigger a _read, then call _read.
  // Note that this may be asynchronous, or synchronous.  Yes, it is
  // deeply ugly to write APIs this way, but that still doesn't mean
  // that the Readable class should behave improperly, as streams are
  // designed to be sync/async agnostic.
  // Take note if the _read call is sync or async (ie, if the read call
  // has returned yet), so that we know whether or not it's safe to emit
  // 'readable' etc.
  //
  // 3. Actually pull the requested chunks out of the buffer and return.

  // if we need a readable event, then we need to do some reading.
  var doRead = state.needReadable;
  debug('need readable', doRead);

  // if we currently have less than the highWaterMark, then also read some
  if (state.length === 0 || state.length - n < state.highWaterMark) {
    doRead = true;
    debug('length less than watermark', doRead);
  }

  // however, if we've ended, then there's no point, and if we're already
  // reading, then it's unnecessary.
  if (state.ended || state.reading) {
    doRead = false;
    debug('reading or ended', doRead);
  } else if (doRead) {
    debug('do read');
    state.reading = true;
    state.sync = true;
    // if the length is currently zero, then we *need* a readable event.
    if (state.length === 0) state.needReadable = true;
    // call internal read method
    this._read(state.highWaterMark);
    state.sync = false;
    // If _read pushed data synchronously, then `reading` will be false,
    // and we need to re-evaluate how much data we can return to the user.
    if (!state.reading) n = howMuchToRead(nOrig, state);
  }

  var ret;
  if (n > 0) ret = fromList(n, state);else ret = null;

  if (ret === null) {
    state.needReadable = true;
    n = 0;
  } else {
    state.length -= n;
  }

  if (state.length === 0) {
    // If we have nothing in the buffer, then we want to know
    // as soon as we *do* get something into the buffer.
    if (!state.ended) state.needReadable = true;

    // If we tried to read() past the EOF, then emit end on the next tick.
    if (nOrig !== n && state.ended) endReadable(this);
  }

  if (ret !== null) this.emit('data', ret);

  return ret;
};

function chunkInvalid(state, chunk) {
  var er = null;
  if (!Buffer.isBuffer(chunk) && typeof chunk !== 'string' && chunk !== null && chunk !== undefined && !state.objectMode) {
    er = new TypeError('Invalid non-string/buffer chunk');
  }
  return er;
}

function onEofChunk(stream, state) {
  if (state.ended) return;
  if (state.decoder) {
    var chunk = state.decoder.end();
    if (chunk && chunk.length) {
      state.buffer.push(chunk);
      state.length += state.objectMode ? 1 : chunk.length;
    }
  }
  state.ended = true;

  // emit 'readable' now to make sure it gets picked up.
  emitReadable(stream);
}

// Don't emit readable right away in sync mode, because this can trigger
// another read() call => stack overflow.  This way, it might trigger
// a nextTick recursion warning, but that's not so bad.
function emitReadable(stream) {
  var state = stream._readableState;
  state.needReadable = false;
  if (!state.emittedReadable) {
    debug('emitReadable', state.flowing);
    state.emittedReadable = true;
    if (state.sync) nextTick(emitReadable_, stream);else emitReadable_(stream);
  }
}

function emitReadable_(stream) {
  debug('emit readable');
  stream.emit('readable');
  flow(stream);
}

// at this point, the user has presumably seen the 'readable' event,
// and called read() to consume some data.  that may have triggered
// in turn another _read(n) call, in which case reading = true if
// it's in progress.
// However, if we're not ended, or reading, and the length < hwm,
// then go ahead and try to read some more preemptively.
function maybeReadMore(stream, state) {
  if (!state.readingMore) {
    state.readingMore = true;
    nextTick(maybeReadMore_, stream, state);
  }
}

function maybeReadMore_(stream, state) {
  var len = state.length;
  while (!state.reading && !state.flowing && !state.ended && state.length < state.highWaterMark) {
    debug('maybeReadMore read 0');
    stream.read(0);
    if (len === state.length)
      // didn't get any data, stop spinning.
      break;else len = state.length;
  }
  state.readingMore = false;
}

// abstract method.  to be overridden in specific implementation classes.
// call cb(er, data) where data is <= n in length.
// for virtual (non-string, non-buffer) streams, "length" is somewhat
// arbitrary, and perhaps not very meaningful.
Readable.prototype._read = function (n) {
  this.emit('error', new Error('not implemented'));
};

Readable.prototype.pipe = function (dest, pipeOpts) {
  var src = this;
  var state = this._readableState;

  switch (state.pipesCount) {
    case 0:
      state.pipes = dest;
      break;
    case 1:
      state.pipes = [state.pipes, dest];
      break;
    default:
      state.pipes.push(dest);
      break;
  }
  state.pipesCount += 1;
  debug('pipe count=%d opts=%j', state.pipesCount, pipeOpts);

  var doEnd = (!pipeOpts || pipeOpts.end !== false);

  var endFn = doEnd ? onend : cleanup;
  if (state.endEmitted) nextTick(endFn);else src.once('end', endFn);

  dest.on('unpipe', onunpipe);
  function onunpipe(readable) {
    debug('onunpipe');
    if (readable === src) {
      cleanup();
    }
  }

  function onend() {
    debug('onend');
    dest.end();
  }

  // when the dest drains, it reduces the awaitDrain counter
  // on the source.  This would be more elegant with a .once()
  // handler in flow(), but adding and removing repeatedly is
  // too slow.
  var ondrain = pipeOnDrain(src);
  dest.on('drain', ondrain);

  var cleanedUp = false;
  function cleanup() {
    debug('cleanup');
    // cleanup event handlers once the pipe is broken
    dest.removeListener('close', onclose);
    dest.removeListener('finish', onfinish);
    dest.removeListener('drain', ondrain);
    dest.removeListener('error', onerror);
    dest.removeListener('unpipe', onunpipe);
    src.removeListener('end', onend);
    src.removeListener('end', cleanup);
    src.removeListener('data', ondata);

    cleanedUp = true;

    // if the reader is waiting for a drain event from this
    // specific writer, then it would cause it to never start
    // flowing again.
    // So, if this is awaiting a drain, then we just call it now.
    // If we don't know, then assume that we are waiting for one.
    if (state.awaitDrain && (!dest._writableState || dest._writableState.needDrain)) ondrain();
  }

  // If the user pushes more data while we're writing to dest then we'll end up
  // in ondata again. However, we only want to increase awaitDrain once because
  // dest will only emit one 'drain' event for the multiple writes.
  // => Introduce a guard on increasing awaitDrain.
  var increasedAwaitDrain = false;
  src.on('data', ondata);
  function ondata(chunk) {
    debug('ondata');
    increasedAwaitDrain = false;
    var ret = dest.write(chunk);
    if (false === ret && !increasedAwaitDrain) {
      // If the user unpiped during `dest.write()`, it is possible
      // to get stuck in a permanently paused state if that write
      // also returned false.
      // => Check whether `dest` is still a piping destination.
      if ((state.pipesCount === 1 && state.pipes === dest || state.pipesCount > 1 && indexOf(state.pipes, dest) !== -1) && !cleanedUp) {
        debug('false write response, pause', src._readableState.awaitDrain);
        src._readableState.awaitDrain++;
        increasedAwaitDrain = true;
      }
      src.pause();
    }
  }

  // if the dest has an error, then stop piping into it.
  // however, don't suppress the throwing behavior for this.
  function onerror(er) {
    debug('onerror', er);
    unpipe();
    dest.removeListener('error', onerror);
    if (listenerCount(dest, 'error') === 0) dest.emit('error', er);
  }

  // Make sure our error handler is attached before userland ones.
  prependListener(dest, 'error', onerror);

  // Both close and finish should trigger unpipe, but only once.
  function onclose() {
    dest.removeListener('finish', onfinish);
    unpipe();
  }
  dest.once('close', onclose);
  function onfinish() {
    debug('onfinish');
    dest.removeListener('close', onclose);
    unpipe();
  }
  dest.once('finish', onfinish);

  function unpipe() {
    debug('unpipe');
    src.unpipe(dest);
  }

  // tell the dest that it's being piped to
  dest.emit('pipe', src);

  // start the flow if it hasn't been started already.
  if (!state.flowing) {
    debug('pipe resume');
    src.resume();
  }

  return dest;
};

function pipeOnDrain(src) {
  return function () {
    var state = src._readableState;
    debug('pipeOnDrain', state.awaitDrain);
    if (state.awaitDrain) state.awaitDrain--;
    if (state.awaitDrain === 0 && src.listeners('data').length) {
      state.flowing = true;
      flow(src);
    }
  };
}

Readable.prototype.unpipe = function (dest) {
  var state = this._readableState;

  // if we're not piping anywhere, then do nothing.
  if (state.pipesCount === 0) return this;

  // just one destination.  most common case.
  if (state.pipesCount === 1) {
    // passed in one, but it's not the right one.
    if (dest && dest !== state.pipes) return this;

    if (!dest) dest = state.pipes;

    // got a match.
    state.pipes = null;
    state.pipesCount = 0;
    state.flowing = false;
    if (dest) dest.emit('unpipe', this);
    return this;
  }

  // slow case. multiple pipe destinations.

  if (!dest) {
    // remove all.
    var dests = state.pipes;
    var len = state.pipesCount;
    state.pipes = null;
    state.pipesCount = 0;
    state.flowing = false;

    for (var _i = 0; _i < len; _i++) {
      dests[_i].emit('unpipe', this);
    }return this;
  }

  // try to find the right one.
  var i = indexOf(state.pipes, dest);
  if (i === -1) return this;

  state.pipes.splice(i, 1);
  state.pipesCount -= 1;
  if (state.pipesCount === 1) state.pipes = state.pipes[0];

  dest.emit('unpipe', this);

  return this;
};

// set up data events if they are asked for
// Ensure readable listeners eventually get something
Readable.prototype.on = function (ev, fn) {
  var res = EventEmitter.prototype.on.call(this, ev, fn);

  if (ev === 'data') {
    // Start flowing on next tick if stream isn't explicitly paused
    if (this._readableState.flowing !== false) this.resume();
  } else if (ev === 'readable') {
    var state = this._readableState;
    if (!state.endEmitted && !state.readableListening) {
      state.readableListening = state.needReadable = true;
      state.emittedReadable = false;
      if (!state.reading) {
        nextTick(nReadingNextTick, this);
      } else if (state.length) {
        emitReadable(this);
      }
    }
  }

  return res;
};
Readable.prototype.addListener = Readable.prototype.on;

function nReadingNextTick(self) {
  debug('readable nexttick read 0');
  self.read(0);
}

// pause() and resume() are remnants of the legacy readable stream API
// If the user uses them, then switch into old mode.
Readable.prototype.resume = function () {
  var state = this._readableState;
  if (!state.flowing) {
    debug('resume');
    state.flowing = true;
    resume(this, state);
  }
  return this;
};

function resume(stream, state) {
  if (!state.resumeScheduled) {
    state.resumeScheduled = true;
    nextTick(resume_, stream, state);
  }
}

function resume_(stream, state) {
  if (!state.reading) {
    debug('resume read 0');
    stream.read(0);
  }

  state.resumeScheduled = false;
  state.awaitDrain = 0;
  stream.emit('resume');
  flow(stream);
  if (state.flowing && !state.reading) stream.read(0);
}

Readable.prototype.pause = function () {
  debug('call pause flowing=%j', this._readableState.flowing);
  if (false !== this._readableState.flowing) {
    debug('pause');
    this._readableState.flowing = false;
    this.emit('pause');
  }
  return this;
};

function flow(stream) {
  var state = stream._readableState;
  debug('flow', state.flowing);
  while (state.flowing && stream.read() !== null) {}
}

// wrap an old-style stream as the async data source.
// This is *not* part of the readable stream interface.
// It is an ugly unfortunate mess of history.
Readable.prototype.wrap = function (stream) {
  var state = this._readableState;
  var paused = false;

  var self = this;
  stream.on('end', function () {
    debug('wrapped end');
    if (state.decoder && !state.ended) {
      var chunk = state.decoder.end();
      if (chunk && chunk.length) self.push(chunk);
    }

    self.push(null);
  });

  stream.on('data', function (chunk) {
    debug('wrapped data');
    if (state.decoder) chunk = state.decoder.write(chunk);

    // don't skip over falsy values in objectMode
    if (state.objectMode && (chunk === null || chunk === undefined)) return;else if (!state.objectMode && (!chunk || !chunk.length)) return;

    var ret = self.push(chunk);
    if (!ret) {
      paused = true;
      stream.pause();
    }
  });

  // proxy all the other methods.
  // important when wrapping filters and duplexes.
  for (var i in stream) {
    if (this[i] === undefined && typeof stream[i] === 'function') {
      this[i] = function (method) {
        return function () {
          return stream[method].apply(stream, arguments);
        };
      }(i);
    }
  }

  // proxy certain important events.
  var events = ['error', 'close', 'destroy', 'pause', 'resume'];
  forEach(events, function (ev) {
    stream.on(ev, self.emit.bind(self, ev));
  });

  // when we try to consume some more bytes, simply unpause the
  // underlying stream.
  self._read = function (n) {
    debug('wrapped _read', n);
    if (paused) {
      paused = false;
      stream.resume();
    }
  };

  return self;
};

// exposed for testing purposes only.
Readable._fromList = fromList;

// Pluck off n bytes from an array of buffers.
// Length is the combined lengths of all the buffers in the list.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function fromList(n, state) {
  // nothing buffered
  if (state.length === 0) return null;

  var ret;
  if (state.objectMode) ret = state.buffer.shift();else if (!n || n >= state.length) {
    // read it all, truncate the list
    if (state.decoder) ret = state.buffer.join('');else if (state.buffer.length === 1) ret = state.buffer.head.data;else ret = state.buffer.concat(state.length);
    state.buffer.clear();
  } else {
    // read part of list
    ret = fromListPartial(n, state.buffer, state.decoder);
  }

  return ret;
}

// Extracts only enough buffered data to satisfy the amount requested.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function fromListPartial(n, list, hasStrings) {
  var ret;
  if (n < list.head.data.length) {
    // slice is the same for buffers and strings
    ret = list.head.data.slice(0, n);
    list.head.data = list.head.data.slice(n);
  } else if (n === list.head.data.length) {
    // first chunk is a perfect match
    ret = list.shift();
  } else {
    // result spans more than one buffer
    ret = hasStrings ? copyFromBufferString(n, list) : copyFromBuffer(n, list);
  }
  return ret;
}

// Copies a specified amount of characters from the list of buffered data
// chunks.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function copyFromBufferString(n, list) {
  var p = list.head;
  var c = 1;
  var ret = p.data;
  n -= ret.length;
  while (p = p.next) {
    var str = p.data;
    var nb = n > str.length ? str.length : n;
    if (nb === str.length) ret += str;else ret += str.slice(0, n);
    n -= nb;
    if (n === 0) {
      if (nb === str.length) {
        ++c;
        if (p.next) list.head = p.next;else list.head = list.tail = null;
      } else {
        list.head = p;
        p.data = str.slice(nb);
      }
      break;
    }
    ++c;
  }
  list.length -= c;
  return ret;
}

// Copies a specified amount of bytes from the list of buffered data chunks.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function copyFromBuffer(n, list) {
  var ret = Buffer.allocUnsafe(n);
  var p = list.head;
  var c = 1;
  p.data.copy(ret);
  n -= p.data.length;
  while (p = p.next) {
    var buf = p.data;
    var nb = n > buf.length ? buf.length : n;
    buf.copy(ret, ret.length - n, 0, nb);
    n -= nb;
    if (n === 0) {
      if (nb === buf.length) {
        ++c;
        if (p.next) list.head = p.next;else list.head = list.tail = null;
      } else {
        list.head = p;
        p.data = buf.slice(nb);
      }
      break;
    }
    ++c;
  }
  list.length -= c;
  return ret;
}

function endReadable(stream) {
  var state = stream._readableState;

  // If we get here before consuming all the bytes, then that is a
  // bug in node.  Should never happen.
  if (state.length > 0) throw new Error('"endReadable()" called on non-empty stream');

  if (!state.endEmitted) {
    state.ended = true;
    nextTick(endReadableNT, state, stream);
  }
}

function endReadableNT(state, stream) {
  // Check that we didn't get one last unshift.
  if (!state.endEmitted && state.length === 0) {
    state.endEmitted = true;
    stream.readable = false;
    stream.emit('end');
  }
}

function forEach(xs, f) {
  for (var i = 0, l = xs.length; i < l; i++) {
    f(xs[i], i);
  }
}

function indexOf(xs, x) {
  for (var i = 0, l = xs.length; i < l; i++) {
    if (xs[i] === x) return i;
  }
  return -1;
}

// A bit simpler than readable streams.
Writable.WritableState = WritableState;
inherits$1(Writable, EventEmitter);

function nop() {}

function WriteReq(chunk, encoding, cb) {
  this.chunk = chunk;
  this.encoding = encoding;
  this.callback = cb;
  this.next = null;
}

function WritableState(options, stream) {
  Object.defineProperty(this, 'buffer', {
    get: deprecate(function () {
      return this.getBuffer();
    }, '_writableState.buffer is deprecated. Use _writableState.getBuffer ' + 'instead.')
  });
  options = options || {};

  // object stream flag to indicate whether or not this stream
  // contains buffers or objects.
  this.objectMode = !!options.objectMode;

  if (stream instanceof Duplex) this.objectMode = this.objectMode || !!options.writableObjectMode;

  // the point at which write() starts returning false
  // Note: 0 is a valid value, means that we always return false if
  // the entire buffer is not flushed immediately on write()
  var hwm = options.highWaterMark;
  var defaultHwm = this.objectMode ? 16 : 16 * 1024;
  this.highWaterMark = hwm || hwm === 0 ? hwm : defaultHwm;

  // cast to ints.
  this.highWaterMark = ~ ~this.highWaterMark;

  this.needDrain = false;
  // at the start of calling end()
  this.ending = false;
  // when end() has been called, and returned
  this.ended = false;
  // when 'finish' is emitted
  this.finished = false;

  // should we decode strings into buffers before passing to _write?
  // this is here so that some node-core streams can optimize string
  // handling at a lower level.
  var noDecode = options.decodeStrings === false;
  this.decodeStrings = !noDecode;

  // Crypto is kind of old and crusty.  Historically, its default string
  // encoding is 'binary' so we have to make this configurable.
  // Everything else in the universe uses 'utf8', though.
  this.defaultEncoding = options.defaultEncoding || 'utf8';

  // not an actual buffer we keep track of, but a measurement
  // of how much we're waiting to get pushed to some underlying
  // socket or file.
  this.length = 0;

  // a flag to see when we're in the middle of a write.
  this.writing = false;

  // when true all writes will be buffered until .uncork() call
  this.corked = 0;

  // a flag to be able to tell if the onwrite cb is called immediately,
  // or on a later tick.  We set this to true at first, because any
  // actions that shouldn't happen until "later" should generally also
  // not happen before the first write call.
  this.sync = true;

  // a flag to know if we're processing previously buffered items, which
  // may call the _write() callback in the same tick, so that we don't
  // end up in an overlapped onwrite situation.
  this.bufferProcessing = false;

  // the callback that's passed to _write(chunk,cb)
  this.onwrite = function (er) {
    onwrite(stream, er);
  };

  // the callback that the user supplies to write(chunk,encoding,cb)
  this.writecb = null;

  // the amount that is being written when _write is called.
  this.writelen = 0;

  this.bufferedRequest = null;
  this.lastBufferedRequest = null;

  // number of pending user-supplied write callbacks
  // this must be 0 before 'finish' can be emitted
  this.pendingcb = 0;

  // emit prefinish if the only thing we're waiting for is _write cbs
  // This is relevant for synchronous Transform streams
  this.prefinished = false;

  // True if the error was already emitted and should not be thrown again
  this.errorEmitted = false;

  // count buffered requests
  this.bufferedRequestCount = 0;

  // allocate the first CorkedRequest, there is always
  // one allocated and free to use, and we maintain at most two
  this.corkedRequestsFree = new CorkedRequest(this);
}

WritableState.prototype.getBuffer = function writableStateGetBuffer() {
  var current = this.bufferedRequest;
  var out = [];
  while (current) {
    out.push(current);
    current = current.next;
  }
  return out;
};
function Writable(options) {

  // Writable ctor is applied to Duplexes, though they're not
  // instanceof Writable, they're instanceof Readable.
  if (!(this instanceof Writable) && !(this instanceof Duplex)) return new Writable(options);

  this._writableState = new WritableState(options, this);

  // legacy.
  this.writable = true;

  if (options) {
    if (typeof options.write === 'function') this._write = options.write;

    if (typeof options.writev === 'function') this._writev = options.writev;
  }

  EventEmitter.call(this);
}

// Otherwise people can pipe Writable streams, which is just wrong.
Writable.prototype.pipe = function () {
  this.emit('error', new Error('Cannot pipe, not readable'));
};

function writeAfterEnd(stream, cb) {
  var er = new Error('write after end');
  // TODO: defer error events consistently everywhere, not just the cb
  stream.emit('error', er);
  nextTick(cb, er);
}

// If we get something that is not a buffer, string, null, or undefined,
// and we're not in objectMode, then that's an error.
// Otherwise stream chunks are all considered to be of length=1, and the
// watermarks determine how many objects to keep in the buffer, rather than
// how many bytes or characters.
function validChunk(stream, state, chunk, cb) {
  var valid = true;
  var er = false;
  // Always throw error if a null is written
  // if we are not in object mode then throw
  // if it is not a buffer, string, or undefined.
  if (chunk === null) {
    er = new TypeError('May not write null values to stream');
  } else if (!Buffer$1.isBuffer(chunk) && typeof chunk !== 'string' && chunk !== undefined && !state.objectMode) {
    er = new TypeError('Invalid non-string/buffer chunk');
  }
  if (er) {
    stream.emit('error', er);
    nextTick(cb, er);
    valid = false;
  }
  return valid;
}

Writable.prototype.write = function (chunk, encoding, cb) {
  var state = this._writableState;
  var ret = false;

  if (typeof encoding === 'function') {
    cb = encoding;
    encoding = null;
  }

  if (Buffer$1.isBuffer(chunk)) encoding = 'buffer';else if (!encoding) encoding = state.defaultEncoding;

  if (typeof cb !== 'function') cb = nop;

  if (state.ended) writeAfterEnd(this, cb);else if (validChunk(this, state, chunk, cb)) {
    state.pendingcb++;
    ret = writeOrBuffer(this, state, chunk, encoding, cb);
  }

  return ret;
};

Writable.prototype.cork = function () {
  var state = this._writableState;

  state.corked++;
};

Writable.prototype.uncork = function () {
  var state = this._writableState;

  if (state.corked) {
    state.corked--;

    if (!state.writing && !state.corked && !state.finished && !state.bufferProcessing && state.bufferedRequest) clearBuffer(this, state);
  }
};

Writable.prototype.setDefaultEncoding = function setDefaultEncoding(encoding) {
  // node::ParseEncoding() requires lower case.
  if (typeof encoding === 'string') encoding = encoding.toLowerCase();
  if (!(['hex', 'utf8', 'utf-8', 'ascii', 'binary', 'base64', 'ucs2', 'ucs-2', 'utf16le', 'utf-16le', 'raw'].indexOf((encoding + '').toLowerCase()) > -1)) throw new TypeError('Unknown encoding: ' + encoding);
  this._writableState.defaultEncoding = encoding;
  return this;
};

function decodeChunk(state, chunk, encoding) {
  if (!state.objectMode && state.decodeStrings !== false && typeof chunk === 'string') {
    chunk = Buffer$1.from(chunk, encoding);
  }
  return chunk;
}

// if we're already writing something, then just put this
// in the queue, and wait our turn.  Otherwise, call _write
// If we return false, then we need a drain event, so set that flag.
function writeOrBuffer(stream, state, chunk, encoding, cb) {
  chunk = decodeChunk(state, chunk, encoding);

  if (Buffer$1.isBuffer(chunk)) encoding = 'buffer';
  var len = state.objectMode ? 1 : chunk.length;

  state.length += len;

  var ret = state.length < state.highWaterMark;
  // we must ensure that previous needDrain will not be reset to false.
  if (!ret) state.needDrain = true;

  if (state.writing || state.corked) {
    var last = state.lastBufferedRequest;
    state.lastBufferedRequest = new WriteReq(chunk, encoding, cb);
    if (last) {
      last.next = state.lastBufferedRequest;
    } else {
      state.bufferedRequest = state.lastBufferedRequest;
    }
    state.bufferedRequestCount += 1;
  } else {
    doWrite(stream, state, false, len, chunk, encoding, cb);
  }

  return ret;
}

function doWrite(stream, state, writev, len, chunk, encoding, cb) {
  state.writelen = len;
  state.writecb = cb;
  state.writing = true;
  state.sync = true;
  if (writev) stream._writev(chunk, state.onwrite);else stream._write(chunk, encoding, state.onwrite);
  state.sync = false;
}

function onwriteError(stream, state, sync, er, cb) {
  --state.pendingcb;
  if (sync) nextTick(cb, er);else cb(er);

  stream._writableState.errorEmitted = true;
  stream.emit('error', er);
}

function onwriteStateUpdate(state) {
  state.writing = false;
  state.writecb = null;
  state.length -= state.writelen;
  state.writelen = 0;
}

function onwrite(stream, er) {
  var state = stream._writableState;
  var sync = state.sync;
  var cb = state.writecb;

  onwriteStateUpdate(state);

  if (er) onwriteError(stream, state, sync, er, cb);else {
    // Check if we're actually ready to finish, but don't emit yet
    var finished = needFinish(state);

    if (!finished && !state.corked && !state.bufferProcessing && state.bufferedRequest) {
      clearBuffer(stream, state);
    }

    if (sync) {
      /*<replacement>*/
        nextTick(afterWrite, stream, state, finished, cb);
      /*</replacement>*/
    } else {
        afterWrite(stream, state, finished, cb);
      }
  }
}

function afterWrite(stream, state, finished, cb) {
  if (!finished) onwriteDrain(stream, state);
  state.pendingcb--;
  cb();
  finishMaybe(stream, state);
}

// Must force callback to be called on nextTick, so that we don't
// emit 'drain' before the write() consumer gets the 'false' return
// value, and has a chance to attach a 'drain' listener.
function onwriteDrain(stream, state) {
  if (state.length === 0 && state.needDrain) {
    state.needDrain = false;
    stream.emit('drain');
  }
}

// if there's something in the buffer waiting, then process it
function clearBuffer(stream, state) {
  state.bufferProcessing = true;
  var entry = state.bufferedRequest;

  if (stream._writev && entry && entry.next) {
    // Fast case, write everything using _writev()
    var l = state.bufferedRequestCount;
    var buffer = new Array(l);
    var holder = state.corkedRequestsFree;
    holder.entry = entry;

    var count = 0;
    while (entry) {
      buffer[count] = entry;
      entry = entry.next;
      count += 1;
    }

    doWrite(stream, state, true, state.length, buffer, '', holder.finish);

    // doWrite is almost always async, defer these to save a bit of time
    // as the hot path ends with doWrite
    state.pendingcb++;
    state.lastBufferedRequest = null;
    if (holder.next) {
      state.corkedRequestsFree = holder.next;
      holder.next = null;
    } else {
      state.corkedRequestsFree = new CorkedRequest(state);
    }
  } else {
    // Slow case, write chunks one-by-one
    while (entry) {
      var chunk = entry.chunk;
      var encoding = entry.encoding;
      var cb = entry.callback;
      var len = state.objectMode ? 1 : chunk.length;

      doWrite(stream, state, false, len, chunk, encoding, cb);
      entry = entry.next;
      // if we didn't call the onwrite immediately, then
      // it means that we need to wait until it does.
      // also, that means that the chunk and cb are currently
      // being processed, so move the buffer counter past them.
      if (state.writing) {
        break;
      }
    }

    if (entry === null) state.lastBufferedRequest = null;
  }

  state.bufferedRequestCount = 0;
  state.bufferedRequest = entry;
  state.bufferProcessing = false;
}

Writable.prototype._write = function (chunk, encoding, cb) {
  cb(new Error('not implemented'));
};

Writable.prototype._writev = null;

Writable.prototype.end = function (chunk, encoding, cb) {
  var state = this._writableState;

  if (typeof chunk === 'function') {
    cb = chunk;
    chunk = null;
    encoding = null;
  } else if (typeof encoding === 'function') {
    cb = encoding;
    encoding = null;
  }

  if (chunk !== null && chunk !== undefined) this.write(chunk, encoding);

  // .end() fully uncorks
  if (state.corked) {
    state.corked = 1;
    this.uncork();
  }

  // ignore unnecessary end() calls.
  if (!state.ending && !state.finished) endWritable(this, state, cb);
};

function needFinish(state) {
  return state.ending && state.length === 0 && state.bufferedRequest === null && !state.finished && !state.writing;
}

function prefinish(stream, state) {
  if (!state.prefinished) {
    state.prefinished = true;
    stream.emit('prefinish');
  }
}

function finishMaybe(stream, state) {
  var need = needFinish(state);
  if (need) {
    if (state.pendingcb === 0) {
      prefinish(stream, state);
      state.finished = true;
      stream.emit('finish');
    } else {
      prefinish(stream, state);
    }
  }
  return need;
}

function endWritable(stream, state, cb) {
  state.ending = true;
  finishMaybe(stream, state);
  if (cb) {
    if (state.finished) nextTick(cb);else stream.once('finish', cb);
  }
  state.ended = true;
  stream.writable = false;
}

// It seems a linked list but it is not
// there will be only 2 of these for each stream
function CorkedRequest(state) {
  var _this = this;

  this.next = null;
  this.entry = null;

  this.finish = function (err) {
    var entry = _this.entry;
    _this.entry = null;
    while (entry) {
      var cb = entry.callback;
      state.pendingcb--;
      cb(err);
      entry = entry.next;
    }
    if (state.corkedRequestsFree) {
      state.corkedRequestsFree.next = _this;
    } else {
      state.corkedRequestsFree = _this;
    }
  };
}

inherits$1(Duplex, Readable);

var keys = Object.keys(Writable.prototype);
for (var v = 0; v < keys.length; v++) {
  var method = keys[v];
  if (!Duplex.prototype[method]) Duplex.prototype[method] = Writable.prototype[method];
}
function Duplex(options) {
  if (!(this instanceof Duplex)) return new Duplex(options);

  Readable.call(this, options);
  Writable.call(this, options);

  if (options && options.readable === false) this.readable = false;

  if (options && options.writable === false) this.writable = false;

  this.allowHalfOpen = true;
  if (options && options.allowHalfOpen === false) this.allowHalfOpen = false;

  this.once('end', onend);
}

// the no-half-open enforcer
function onend() {
  // if we allow half-open state, or if the writable side ended,
  // then we're ok.
  if (this.allowHalfOpen || this._writableState.ended) return;

  // no more data can be written.
  // But allow more writes to happen in this tick.
  nextTick(onEndNT, this);
}

function onEndNT(self) {
  self.end();
}

// a transform stream is a readable/writable stream where you do
inherits$1(Transform, Duplex);

function TransformState(stream) {
  this.afterTransform = function (er, data) {
    return afterTransform(stream, er, data);
  };

  this.needTransform = false;
  this.transforming = false;
  this.writecb = null;
  this.writechunk = null;
  this.writeencoding = null;
}

function afterTransform(stream, er, data) {
  var ts = stream._transformState;
  ts.transforming = false;

  var cb = ts.writecb;

  if (!cb) return stream.emit('error', new Error('no writecb in Transform class'));

  ts.writechunk = null;
  ts.writecb = null;

  if (data !== null && data !== undefined) stream.push(data);

  cb(er);

  var rs = stream._readableState;
  rs.reading = false;
  if (rs.needReadable || rs.length < rs.highWaterMark) {
    stream._read(rs.highWaterMark);
  }
}
function Transform(options) {
  if (!(this instanceof Transform)) return new Transform(options);

  Duplex.call(this, options);

  this._transformState = new TransformState(this);

  // when the writable side finishes, then flush out anything remaining.
  var stream = this;

  // start out asking for a readable event once data is transformed.
  this._readableState.needReadable = true;

  // we have implemented the _read method, and done the other things
  // that Readable wants before the first _read call, so unset the
  // sync guard flag.
  this._readableState.sync = false;

  if (options) {
    if (typeof options.transform === 'function') this._transform = options.transform;

    if (typeof options.flush === 'function') this._flush = options.flush;
  }

  this.once('prefinish', function () {
    if (typeof this._flush === 'function') this._flush(function (er) {
      done(stream, er);
    });else done(stream);
  });
}

Transform.prototype.push = function (chunk, encoding) {
  this._transformState.needTransform = false;
  return Duplex.prototype.push.call(this, chunk, encoding);
};

// This is the part where you do stuff!
// override this function in implementation classes.
// 'chunk' is an input chunk.
//
// Call `push(newChunk)` to pass along transformed output
// to the readable side.  You may call 'push' zero or more times.
//
// Call `cb(err)` when you are done with this chunk.  If you pass
// an error, then that'll put the hurt on the whole operation.  If you
// never call cb(), then you'll never get another chunk.
Transform.prototype._transform = function (chunk, encoding, cb) {
  throw new Error('Not implemented');
};

Transform.prototype._write = function (chunk, encoding, cb) {
  var ts = this._transformState;
  ts.writecb = cb;
  ts.writechunk = chunk;
  ts.writeencoding = encoding;
  if (!ts.transforming) {
    var rs = this._readableState;
    if (ts.needTransform || rs.needReadable || rs.length < rs.highWaterMark) this._read(rs.highWaterMark);
  }
};

// Doesn't matter what the args are here.
// _transform does all the work.
// That we got here means that the readable side wants more data.
Transform.prototype._read = function (n) {
  var ts = this._transformState;

  if (ts.writechunk !== null && ts.writecb && !ts.transforming) {
    ts.transforming = true;
    this._transform(ts.writechunk, ts.writeencoding, ts.afterTransform);
  } else {
    // mark that we need a transform, so that any data that comes in
    // will get processed, now that we've asked for it.
    ts.needTransform = true;
  }
};

function done(stream, er) {
  if (er) return stream.emit('error', er);

  // if there's nothing in the write buffer, then that means
  // that nothing more will ever be provided
  var ws = stream._writableState;
  var ts = stream._transformState;

  if (ws.length) throw new Error('Calling transform done when ws.length != 0');

  if (ts.transforming) throw new Error('Calling transform done when still transforming');

  return stream.push(null);
}

inherits$1(PassThrough, Transform);
function PassThrough(options) {
  if (!(this instanceof PassThrough)) return new PassThrough(options);

  Transform.call(this, options);
}

PassThrough.prototype._transform = function (chunk, encoding, cb) {
  cb(null, chunk);
};

inherits$1(Stream, EventEmitter);
Stream.Readable = Readable;
Stream.Writable = Writable;
Stream.Duplex = Duplex;
Stream.Transform = Transform;
Stream.PassThrough = PassThrough;

// Backwards-compat with node 0.4.x
Stream.Stream = Stream;

// old-style streams.  Note that the pipe method (the only relevant
// part of this class) is overridden in the Readable class.

function Stream() {
  EventEmitter.call(this);
}

Stream.prototype.pipe = function(dest, options) {
  var source = this;

  function ondata(chunk) {
    if (dest.writable) {
      if (false === dest.write(chunk) && source.pause) {
        source.pause();
      }
    }
  }

  source.on('data', ondata);

  function ondrain() {
    if (source.readable && source.resume) {
      source.resume();
    }
  }

  dest.on('drain', ondrain);

  // If the 'end' option is not supplied, dest.end() will be called when
  // source gets the 'end' or 'close' events.  Only dest.end() once.
  if (!dest._isStdio && (!options || options.end !== false)) {
    source.on('end', onend);
    source.on('close', onclose);
  }

  var didOnEnd = false;
  function onend() {
    if (didOnEnd) return;
    didOnEnd = true;

    dest.end();
  }


  function onclose() {
    if (didOnEnd) return;
    didOnEnd = true;

    if (typeof dest.destroy === 'function') dest.destroy();
  }

  // don't leave dangling pipes when there are errors.
  function onerror(er) {
    cleanup();
    if (EventEmitter.listenerCount(this, 'error') === 0) {
      throw er; // Unhandled stream error in pipe.
    }
  }

  source.on('error', onerror);
  dest.on('error', onerror);

  // remove all the event listeners that were added.
  function cleanup() {
    source.removeListener('data', ondata);
    dest.removeListener('drain', ondrain);

    source.removeListener('end', onend);
    source.removeListener('close', onclose);

    source.removeListener('error', onerror);
    dest.removeListener('error', onerror);

    source.removeListener('end', cleanup);
    source.removeListener('close', cleanup);

    dest.removeListener('close', cleanup);
  }

  source.on('end', cleanup);
  source.on('close', cleanup);

  dest.on('close', cleanup);

  dest.emit('pipe', source);

  // Allow for unix-like usage: A.pipe(B).pipe(C)
  return dest;
};

var stream = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': Stream,
  Readable: Readable,
  Writable: Writable,
  Duplex: Duplex,
  Transform: Transform,
  PassThrough: PassThrough,
  Stream: Stream
});

var require$$0$1 = /*@__PURE__*/getAugmentedNamespace(stream);

var require$$1$1 = /*@__PURE__*/getAugmentedNamespace(stringDecoder);

var hasRequiredSax;

function requireSax () {
	if (hasRequiredSax) return sax;
	hasRequiredSax = 1;
	(function (exports) {
(function (sax) {
		  // wrapper for non-node envs
		  sax.parser = function (strict, opt) {
		    return new SAXParser(strict, opt)
		  };
		  sax.SAXParser = SAXParser;
		  sax.SAXStream = SAXStream;
		  sax.createStream = createStream;

		  // When we pass the MAX_BUFFER_LENGTH position, start checking for buffer overruns.
		  // When we check, schedule the next check for MAX_BUFFER_LENGTH - (max(buffer lengths)),
		  // since that's the earliest that a buffer overrun could occur.  This way, checks are
		  // as rare as required, but as often as necessary to ensure never crossing this bound.
		  // Furthermore, buffers are only tested at most once per write(), so passing a very
		  // large string into write() might have undesirable effects, but this is manageable by
		  // the caller, so it is assumed to be safe.  Thus, a call to write() may, in the extreme
		  // edge case, result in creating at most one complete copy of the string passed in.
		  // Set to Infinity to have unlimited buffers.
		  sax.MAX_BUFFER_LENGTH = 64 * 1024;

		  var buffers = [
		    'comment',
		    'sgmlDecl',
		    'textNode',
		    'tagName',
		    'doctype',
		    'procInstName',
		    'procInstBody',
		    'entity',
		    'attribName',
		    'attribValue',
		    'cdata',
		    'script',
		  ];

		  sax.EVENTS = [
		    'text',
		    'processinginstruction',
		    'sgmldeclaration',
		    'doctype',
		    'comment',
		    'opentagstart',
		    'attribute',
		    'opentag',
		    'closetag',
		    'opencdata',
		    'cdata',
		    'closecdata',
		    'error',
		    'end',
		    'ready',
		    'script',
		    'opennamespace',
		    'closenamespace',
		  ];

		  function SAXParser(strict, opt) {
		    if (!(this instanceof SAXParser)) {
		      return new SAXParser(strict, opt)
		    }

		    var parser = this;
		    clearBuffers(parser);
		    parser.q = parser.c = '';
		    parser.bufferCheckPosition = sax.MAX_BUFFER_LENGTH;
		    parser.opt = opt || {};
		    parser.opt.lowercase = parser.opt.lowercase || parser.opt.lowercasetags;
		    parser.looseCase = parser.opt.lowercase ? 'toLowerCase' : 'toUpperCase';
		    parser.tags = [];
		    parser.closed = parser.closedRoot = parser.sawRoot = false;
		    parser.tag = parser.error = null;
		    parser.strict = !!strict;
		    parser.noscript = !!(strict || parser.opt.noscript);
		    parser.state = S.BEGIN;
		    parser.strictEntities = parser.opt.strictEntities;
		    parser.ENTITIES =
		      parser.strictEntities ?
		        Object.create(sax.XML_ENTITIES)
		      : Object.create(sax.ENTITIES);
		    parser.attribList = [];

		    // namespaces form a prototype chain.
		    // it always points at the current tag,
		    // which protos to its parent tag.
		    if (parser.opt.xmlns) {
		      parser.ns = Object.create(rootNS);
		    }

		    // disallow unquoted attribute values if not otherwise configured
		    // and strict mode is true
		    if (parser.opt.unquotedAttributeValues === undefined) {
		      parser.opt.unquotedAttributeValues = !strict;
		    }

		    // mostly just for error reporting
		    parser.trackPosition = parser.opt.position !== false;
		    if (parser.trackPosition) {
		      parser.position = parser.line = parser.column = 0;
		    }
		    emit(parser, 'onready');
		  }

		  if (!Object.create) {
		    Object.create = function (o) {
		      function F() {}
		      F.prototype = o;
		      var newf = new F();
		      return newf
		    };
		  }

		  if (!Object.keys) {
		    Object.keys = function (o) {
		      var a = [];
		      for (var i in o) if (o.hasOwnProperty(i)) a.push(i);
		      return a
		    };
		  }

		  function checkBufferLength(parser) {
		    var maxAllowed = Math.max(sax.MAX_BUFFER_LENGTH, 10);
		    var maxActual = 0;
		    for (var i = 0, l = buffers.length; i < l; i++) {
		      var len = parser[buffers[i]].length;
		      if (len > maxAllowed) {
		        // Text/cdata nodes can get big, and since they're buffered,
		        // we can get here under normal conditions.
		        // Avoid issues by emitting the text node now,
		        // so at least it won't get any bigger.
		        switch (buffers[i]) {
		          case 'textNode':
		            closeText(parser);
		            break

		          case 'cdata':
		            emitNode(parser, 'oncdata', parser.cdata);
		            parser.cdata = '';
		            break

		          case 'script':
		            emitNode(parser, 'onscript', parser.script);
		            parser.script = '';
		            break

		          default:
		            error(parser, 'Max buffer length exceeded: ' + buffers[i]);
		        }
		      }
		      maxActual = Math.max(maxActual, len);
		    }
		    // schedule the next check for the earliest possible buffer overrun.
		    var m = sax.MAX_BUFFER_LENGTH - maxActual;
		    parser.bufferCheckPosition = m + parser.position;
		  }

		  function clearBuffers(parser) {
		    for (var i = 0, l = buffers.length; i < l; i++) {
		      parser[buffers[i]] = '';
		    }
		  }

		  function flushBuffers(parser) {
		    closeText(parser);
		    if (parser.cdata !== '') {
		      emitNode(parser, 'oncdata', parser.cdata);
		      parser.cdata = '';
		    }
		    if (parser.script !== '') {
		      emitNode(parser, 'onscript', parser.script);
		      parser.script = '';
		    }
		  }

		  SAXParser.prototype = {
		    end: function () {
		      end(this);
		    },
		    write: write,
		    resume: function () {
		      this.error = null;
		      return this
		    },
		    close: function () {
		      return this.write(null)
		    },
		    flush: function () {
		      flushBuffers(this);
		    },
		  };

		  var Stream;
		  try {
		    Stream = require$$0$1.Stream;
		  } catch (ex) {
		    Stream = function () {};
		  }
		  if (!Stream) Stream = function () {};

		  var streamWraps = sax.EVENTS.filter(function (ev) {
		    return ev !== 'error' && ev !== 'end'
		  });

		  function createStream(strict, opt) {
		    return new SAXStream(strict, opt)
		  }

		  function SAXStream(strict, opt) {
		    if (!(this instanceof SAXStream)) {
		      return new SAXStream(strict, opt)
		    }

		    Stream.apply(this);

		    this._parser = new SAXParser(strict, opt);
		    this.writable = true;
		    this.readable = true;

		    var me = this;

		    this._parser.onend = function () {
		      me.emit('end');
		    };

		    this._parser.onerror = function (er) {
		      me.emit('error', er);

		      // if didn't throw, then means error was handled.
		      // go ahead and clear error, so we can write again.
		      me._parser.error = null;
		    };

		    this._decoder = null;

		    streamWraps.forEach(function (ev) {
		      Object.defineProperty(me, 'on' + ev, {
		        get: function () {
		          return me._parser['on' + ev]
		        },
		        set: function (h) {
		          if (!h) {
		            me.removeAllListeners(ev);
		            me._parser['on' + ev] = h;
		            return h
		          }
		          me.on(ev, h);
		        },
		        enumerable: true,
		        configurable: false,
		      });
		    });
		  }

		  SAXStream.prototype = Object.create(Stream.prototype, {
		    constructor: {
		      value: SAXStream,
		    },
		  });

		  SAXStream.prototype.write = function (data) {
		    if (
		      typeof Buffer === 'function' &&
		      typeof Buffer.isBuffer === 'function' &&
		      Buffer.isBuffer(data)
		    ) {
		      if (!this._decoder) {
		        var SD = require$$1$1.StringDecoder;
		        this._decoder = new SD('utf8');
		      }
		      data = this._decoder.write(data);
		    }

		    this._parser.write(data.toString());
		    this.emit('data', data);
		    return true
		  };

		  SAXStream.prototype.end = function (chunk) {
		    if (chunk && chunk.length) {
		      this.write(chunk);
		    }
		    this._parser.end();
		    return true
		  };

		  SAXStream.prototype.on = function (ev, handler) {
		    var me = this;
		    if (!me._parser['on' + ev] && streamWraps.indexOf(ev) !== -1) {
		      me._parser['on' + ev] = function () {
		        var args =
		          arguments.length === 1 ?
		            [arguments[0]]
		          : Array.apply(null, arguments);
		        args.splice(0, 0, ev);
		        me.emit.apply(me, args);
		      };
		    }

		    return Stream.prototype.on.call(me, ev, handler)
		  };

		  // this really needs to be replaced with character classes.
		  // XML allows all manner of ridiculous numbers and digits.
		  var CDATA = '[CDATA[';
		  var DOCTYPE = 'DOCTYPE';
		  var XML_NAMESPACE = 'http://www.w3.org/XML/1998/namespace';
		  var XMLNS_NAMESPACE = 'http://www.w3.org/2000/xmlns/';
		  var rootNS = { xml: XML_NAMESPACE, xmlns: XMLNS_NAMESPACE };

		  // http://www.w3.org/TR/REC-xml/#NT-NameStartChar
		  // This implementation works on strings, a single character at a time
		  // as such, it cannot ever support astral-plane characters (10000-EFFFF)
		  // without a significant breaking change to either this  parser, or the
		  // JavaScript language.  Implementation of an emoji-capable xml parser
		  // is left as an exercise for the reader.
		  var nameStart =
		    /[:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/;

		  var nameBody =
		    /[:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\u00B7\u0300-\u036F\u203F-\u2040.\d-]/;

		  var entityStart =
		    /[#:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/;
		  var entityBody =
		    /[#:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\u00B7\u0300-\u036F\u203F-\u2040.\d-]/;

		  function isWhitespace(c) {
		    return c === ' ' || c === '\n' || c === '\r' || c === '\t'
		  }

		  function isQuote(c) {
		    return c === '"' || c === "'"
		  }

		  function isAttribEnd(c) {
		    return c === '>' || isWhitespace(c)
		  }

		  function isMatch(regex, c) {
		    return regex.test(c)
		  }

		  function notMatch(regex, c) {
		    return !isMatch(regex, c)
		  }

		  var S = 0;
		  sax.STATE = {
		    BEGIN: S++, // leading byte order mark or whitespace
		    BEGIN_WHITESPACE: S++, // leading whitespace
		    TEXT: S++, // general stuff
		    TEXT_ENTITY: S++, // &amp and such.
		    OPEN_WAKA: S++, // <
		    SGML_DECL: S++, // <!BLARG
		    SGML_DECL_QUOTED: S++, // <!BLARG foo "bar
		    DOCTYPE: S++, // <!DOCTYPE
		    DOCTYPE_QUOTED: S++, // <!DOCTYPE "//blah
		    DOCTYPE_DTD: S++, // <!DOCTYPE "//blah" [ ...
		    DOCTYPE_DTD_QUOTED: S++, // <!DOCTYPE "//blah" [ "foo
		    COMMENT_STARTING: S++, // <!-
		    COMMENT: S++, // <!--
		    COMMENT_ENDING: S++, // <!-- blah -
		    COMMENT_ENDED: S++, // <!-- blah --
		    CDATA: S++, // <![CDATA[ something
		    CDATA_ENDING: S++, // ]
		    CDATA_ENDING_2: S++, // ]]
		    PROC_INST: S++, // <?hi
		    PROC_INST_BODY: S++, // <?hi there
		    PROC_INST_ENDING: S++, // <?hi "there" ?
		    OPEN_TAG: S++, // <strong
		    OPEN_TAG_SLASH: S++, // <strong /
		    ATTRIB: S++, // <a
		    ATTRIB_NAME: S++, // <a foo
		    ATTRIB_NAME_SAW_WHITE: S++, // <a foo _
		    ATTRIB_VALUE: S++, // <a foo=
		    ATTRIB_VALUE_QUOTED: S++, // <a foo="bar
		    ATTRIB_VALUE_CLOSED: S++, // <a foo="bar"
		    ATTRIB_VALUE_UNQUOTED: S++, // <a foo=bar
		    ATTRIB_VALUE_ENTITY_Q: S++, // <foo bar="&quot;"
		    ATTRIB_VALUE_ENTITY_U: S++, // <foo bar=&quot
		    CLOSE_TAG: S++, // </a
		    CLOSE_TAG_SAW_WHITE: S++, // </a   >
		    SCRIPT: S++, // <script> ...
		    SCRIPT_ENDING: S++, // <script> ... <
		  };

		  sax.XML_ENTITIES = {
		    amp: '&',
		    gt: '>',
		    lt: '<',
		    quot: '"',
		    apos: "'",
		  };

		  sax.ENTITIES = {
		    amp: '&',
		    gt: '>',
		    lt: '<',
		    quot: '"',
		    apos: "'",
		    AElig: 198,
		    Aacute: 193,
		    Acirc: 194,
		    Agrave: 192,
		    Aring: 197,
		    Atilde: 195,
		    Auml: 196,
		    Ccedil: 199,
		    ETH: 208,
		    Eacute: 201,
		    Ecirc: 202,
		    Egrave: 200,
		    Euml: 203,
		    Iacute: 205,
		    Icirc: 206,
		    Igrave: 204,
		    Iuml: 207,
		    Ntilde: 209,
		    Oacute: 211,
		    Ocirc: 212,
		    Ograve: 210,
		    Oslash: 216,
		    Otilde: 213,
		    Ouml: 214,
		    THORN: 222,
		    Uacute: 218,
		    Ucirc: 219,
		    Ugrave: 217,
		    Uuml: 220,
		    Yacute: 221,
		    aacute: 225,
		    acirc: 226,
		    aelig: 230,
		    agrave: 224,
		    aring: 229,
		    atilde: 227,
		    auml: 228,
		    ccedil: 231,
		    eacute: 233,
		    ecirc: 234,
		    egrave: 232,
		    eth: 240,
		    euml: 235,
		    iacute: 237,
		    icirc: 238,
		    igrave: 236,
		    iuml: 239,
		    ntilde: 241,
		    oacute: 243,
		    ocirc: 244,
		    ograve: 242,
		    oslash: 248,
		    otilde: 245,
		    ouml: 246,
		    szlig: 223,
		    thorn: 254,
		    uacute: 250,
		    ucirc: 251,
		    ugrave: 249,
		    uuml: 252,
		    yacute: 253,
		    yuml: 255,
		    copy: 169,
		    reg: 174,
		    nbsp: 160,
		    iexcl: 161,
		    cent: 162,
		    pound: 163,
		    curren: 164,
		    yen: 165,
		    brvbar: 166,
		    sect: 167,
		    uml: 168,
		    ordf: 170,
		    laquo: 171,
		    not: 172,
		    shy: 173,
		    macr: 175,
		    deg: 176,
		    plusmn: 177,
		    sup1: 185,
		    sup2: 178,
		    sup3: 179,
		    acute: 180,
		    micro: 181,
		    para: 182,
		    middot: 183,
		    cedil: 184,
		    ordm: 186,
		    raquo: 187,
		    frac14: 188,
		    frac12: 189,
		    frac34: 190,
		    iquest: 191,
		    times: 215,
		    divide: 247,
		    OElig: 338,
		    oelig: 339,
		    Scaron: 352,
		    scaron: 353,
		    Yuml: 376,
		    fnof: 402,
		    circ: 710,
		    tilde: 732,
		    Alpha: 913,
		    Beta: 914,
		    Gamma: 915,
		    Delta: 916,
		    Epsilon: 917,
		    Zeta: 918,
		    Eta: 919,
		    Theta: 920,
		    Iota: 921,
		    Kappa: 922,
		    Lambda: 923,
		    Mu: 924,
		    Nu: 925,
		    Xi: 926,
		    Omicron: 927,
		    Pi: 928,
		    Rho: 929,
		    Sigma: 931,
		    Tau: 932,
		    Upsilon: 933,
		    Phi: 934,
		    Chi: 935,
		    Psi: 936,
		    Omega: 937,
		    alpha: 945,
		    beta: 946,
		    gamma: 947,
		    delta: 948,
		    epsilon: 949,
		    zeta: 950,
		    eta: 951,
		    theta: 952,
		    iota: 953,
		    kappa: 954,
		    lambda: 955,
		    mu: 956,
		    nu: 957,
		    xi: 958,
		    omicron: 959,
		    pi: 960,
		    rho: 961,
		    sigmaf: 962,
		    sigma: 963,
		    tau: 964,
		    upsilon: 965,
		    phi: 966,
		    chi: 967,
		    psi: 968,
		    omega: 969,
		    thetasym: 977,
		    upsih: 978,
		    piv: 982,
		    ensp: 8194,
		    emsp: 8195,
		    thinsp: 8201,
		    zwnj: 8204,
		    zwj: 8205,
		    lrm: 8206,
		    rlm: 8207,
		    ndash: 8211,
		    mdash: 8212,
		    lsquo: 8216,
		    rsquo: 8217,
		    sbquo: 8218,
		    ldquo: 8220,
		    rdquo: 8221,
		    bdquo: 8222,
		    dagger: 8224,
		    Dagger: 8225,
		    bull: 8226,
		    hellip: 8230,
		    permil: 8240,
		    prime: 8242,
		    Prime: 8243,
		    lsaquo: 8249,
		    rsaquo: 8250,
		    oline: 8254,
		    frasl: 8260,
		    euro: 8364,
		    image: 8465,
		    weierp: 8472,
		    real: 8476,
		    trade: 8482,
		    alefsym: 8501,
		    larr: 8592,
		    uarr: 8593,
		    rarr: 8594,
		    darr: 8595,
		    harr: 8596,
		    crarr: 8629,
		    lArr: 8656,
		    uArr: 8657,
		    rArr: 8658,
		    dArr: 8659,
		    hArr: 8660,
		    forall: 8704,
		    part: 8706,
		    exist: 8707,
		    empty: 8709,
		    nabla: 8711,
		    isin: 8712,
		    notin: 8713,
		    ni: 8715,
		    prod: 8719,
		    sum: 8721,
		    minus: 8722,
		    lowast: 8727,
		    radic: 8730,
		    prop: 8733,
		    infin: 8734,
		    ang: 8736,
		    and: 8743,
		    or: 8744,
		    cap: 8745,
		    cup: 8746,
		    int: 8747,
		    there4: 8756,
		    sim: 8764,
		    cong: 8773,
		    asymp: 8776,
		    ne: 8800,
		    equiv: 8801,
		    le: 8804,
		    ge: 8805,
		    sub: 8834,
		    sup: 8835,
		    nsub: 8836,
		    sube: 8838,
		    supe: 8839,
		    oplus: 8853,
		    otimes: 8855,
		    perp: 8869,
		    sdot: 8901,
		    lceil: 8968,
		    rceil: 8969,
		    lfloor: 8970,
		    rfloor: 8971,
		    lang: 9001,
		    rang: 9002,
		    loz: 9674,
		    spades: 9824,
		    clubs: 9827,
		    hearts: 9829,
		    diams: 9830,
		  };

		  Object.keys(sax.ENTITIES).forEach(function (key) {
		    var e = sax.ENTITIES[key];
		    var s = typeof e === 'number' ? String.fromCharCode(e) : e;
		    sax.ENTITIES[key] = s;
		  });

		  for (var s in sax.STATE) {
		    sax.STATE[sax.STATE[s]] = s;
		  }

		  // shorthand
		  S = sax.STATE;

		  function emit(parser, event, data) {
		    parser[event] && parser[event](data);
		  }

		  function emitNode(parser, nodeType, data) {
		    if (parser.textNode) closeText(parser);
		    emit(parser, nodeType, data);
		  }

		  function closeText(parser) {
		    parser.textNode = textopts(parser.opt, parser.textNode);
		    if (parser.textNode) emit(parser, 'ontext', parser.textNode);
		    parser.textNode = '';
		  }

		  function textopts(opt, text) {
		    if (opt.trim) text = text.trim();
		    if (opt.normalize) text = text.replace(/\s+/g, ' ');
		    return text
		  }

		  function error(parser, er) {
		    closeText(parser);
		    if (parser.trackPosition) {
		      er +=
		        '\nLine: ' +
		        parser.line +
		        '\nColumn: ' +
		        parser.column +
		        '\nChar: ' +
		        parser.c;
		    }
		    er = new Error(er);
		    parser.error = er;
		    emit(parser, 'onerror', er);
		    return parser
		  }

		  function end(parser) {
		    if (parser.sawRoot && !parser.closedRoot)
		      strictFail(parser, 'Unclosed root tag');
		    if (
		      parser.state !== S.BEGIN &&
		      parser.state !== S.BEGIN_WHITESPACE &&
		      parser.state !== S.TEXT
		    ) {
		      error(parser, 'Unexpected end');
		    }
		    closeText(parser);
		    parser.c = '';
		    parser.closed = true;
		    emit(parser, 'onend');
		    SAXParser.call(parser, parser.strict, parser.opt);
		    return parser
		  }

		  function strictFail(parser, message) {
		    if (typeof parser !== 'object' || !(parser instanceof SAXParser)) {
		      throw new Error('bad call to strictFail')
		    }
		    if (parser.strict) {
		      error(parser, message);
		    }
		  }

		  function newTag(parser) {
		    if (!parser.strict) parser.tagName = parser.tagName[parser.looseCase]();
		    var parent = parser.tags[parser.tags.length - 1] || parser;
		    var tag = (parser.tag = { name: parser.tagName, attributes: {} });

		    // will be overridden if tag contails an xmlns="foo" or xmlns:foo="bar"
		    if (parser.opt.xmlns) {
		      tag.ns = parent.ns;
		    }
		    parser.attribList.length = 0;
		    emitNode(parser, 'onopentagstart', tag);
		  }

		  function qname(name, attribute) {
		    var i = name.indexOf(':');
		    var qualName = i < 0 ? ['', name] : name.split(':');
		    var prefix = qualName[0];
		    var local = qualName[1];

		    // <x "xmlns"="http://foo">
		    if (attribute && name === 'xmlns') {
		      prefix = 'xmlns';
		      local = '';
		    }

		    return { prefix: prefix, local: local }
		  }

		  function attrib(parser) {
		    if (!parser.strict) {
		      parser.attribName = parser.attribName[parser.looseCase]();
		    }

		    if (
		      parser.attribList.indexOf(parser.attribName) !== -1 ||
		      parser.tag.attributes.hasOwnProperty(parser.attribName)
		    ) {
		      parser.attribName = parser.attribValue = '';
		      return
		    }

		    if (parser.opt.xmlns) {
		      var qn = qname(parser.attribName, true);
		      var prefix = qn.prefix;
		      var local = qn.local;

		      if (prefix === 'xmlns') {
		        // namespace binding attribute. push the binding into scope
		        if (local === 'xml' && parser.attribValue !== XML_NAMESPACE) {
		          strictFail(
		            parser,
		            'xml: prefix must be bound to ' +
		              XML_NAMESPACE +
		              '\n' +
		              'Actual: ' +
		              parser.attribValue
		          );
		        } else if (
		          local === 'xmlns' &&
		          parser.attribValue !== XMLNS_NAMESPACE
		        ) {
		          strictFail(
		            parser,
		            'xmlns: prefix must be bound to ' +
		              XMLNS_NAMESPACE +
		              '\n' +
		              'Actual: ' +
		              parser.attribValue
		          );
		        } else {
		          var tag = parser.tag;
		          var parent = parser.tags[parser.tags.length - 1] || parser;
		          if (tag.ns === parent.ns) {
		            tag.ns = Object.create(parent.ns);
		          }
		          tag.ns[local] = parser.attribValue;
		        }
		      }

		      // defer onattribute events until all attributes have been seen
		      // so any new bindings can take effect. preserve attribute order
		      // so deferred events can be emitted in document order
		      parser.attribList.push([parser.attribName, parser.attribValue]);
		    } else {
		      // in non-xmlns mode, we can emit the event right away
		      parser.tag.attributes[parser.attribName] = parser.attribValue;
		      emitNode(parser, 'onattribute', {
		        name: parser.attribName,
		        value: parser.attribValue,
		      });
		    }

		    parser.attribName = parser.attribValue = '';
		  }

		  function openTag(parser, selfClosing) {
		    if (parser.opt.xmlns) {
		      // emit namespace binding events
		      var tag = parser.tag;

		      // add namespace info to tag
		      var qn = qname(parser.tagName);
		      tag.prefix = qn.prefix;
		      tag.local = qn.local;
		      tag.uri = tag.ns[qn.prefix] || '';

		      if (tag.prefix && !tag.uri) {
		        strictFail(
		          parser,
		          'Unbound namespace prefix: ' + JSON.stringify(parser.tagName)
		        );
		        tag.uri = qn.prefix;
		      }

		      var parent = parser.tags[parser.tags.length - 1] || parser;
		      if (tag.ns && parent.ns !== tag.ns) {
		        Object.keys(tag.ns).forEach(function (p) {
		          emitNode(parser, 'onopennamespace', {
		            prefix: p,
		            uri: tag.ns[p],
		          });
		        });
		      }

		      // handle deferred onattribute events
		      // Note: do not apply default ns to attributes:
		      //   http://www.w3.org/TR/REC-xml-names/#defaulting
		      for (var i = 0, l = parser.attribList.length; i < l; i++) {
		        var nv = parser.attribList[i];
		        var name = nv[0];
		        var value = nv[1];
		        var qualName = qname(name, true);
		        var prefix = qualName.prefix;
		        var local = qualName.local;
		        var uri = prefix === '' ? '' : tag.ns[prefix] || '';
		        var a = {
		          name: name,
		          value: value,
		          prefix: prefix,
		          local: local,
		          uri: uri,
		        };

		        // if there's any attributes with an undefined namespace,
		        // then fail on them now.
		        if (prefix && prefix !== 'xmlns' && !uri) {
		          strictFail(
		            parser,
		            'Unbound namespace prefix: ' + JSON.stringify(prefix)
		          );
		          a.uri = prefix;
		        }
		        parser.tag.attributes[name] = a;
		        emitNode(parser, 'onattribute', a);
		      }
		      parser.attribList.length = 0;
		    }

		    parser.tag.isSelfClosing = !!selfClosing;

		    // process the tag
		    parser.sawRoot = true;
		    parser.tags.push(parser.tag);
		    emitNode(parser, 'onopentag', parser.tag);
		    if (!selfClosing) {
		      // special case for <script> in non-strict mode.
		      if (!parser.noscript && parser.tagName.toLowerCase() === 'script') {
		        parser.state = S.SCRIPT;
		      } else {
		        parser.state = S.TEXT;
		      }
		      parser.tag = null;
		      parser.tagName = '';
		    }
		    parser.attribName = parser.attribValue = '';
		    parser.attribList.length = 0;
		  }

		  function closeTag(parser) {
		    if (!parser.tagName) {
		      strictFail(parser, 'Weird empty close tag.');
		      parser.textNode += '</>';
		      parser.state = S.TEXT;
		      return
		    }

		    if (parser.script) {
		      if (parser.tagName !== 'script') {
		        parser.script += '</' + parser.tagName + '>';
		        parser.tagName = '';
		        parser.state = S.SCRIPT;
		        return
		      }
		      emitNode(parser, 'onscript', parser.script);
		      parser.script = '';
		    }

		    // first make sure that the closing tag actually exists.
		    // <a><b></c></b></a> will close everything, otherwise.
		    var t = parser.tags.length;
		    var tagName = parser.tagName;
		    if (!parser.strict) {
		      tagName = tagName[parser.looseCase]();
		    }
		    var closeTo = tagName;
		    while (t--) {
		      var close = parser.tags[t];
		      if (close.name !== closeTo) {
		        // fail the first time in strict mode
		        strictFail(parser, 'Unexpected close tag');
		      } else {
		        break
		      }
		    }

		    // didn't find it.  we already failed for strict, so just abort.
		    if (t < 0) {
		      strictFail(parser, 'Unmatched closing tag: ' + parser.tagName);
		      parser.textNode += '</' + parser.tagName + '>';
		      parser.state = S.TEXT;
		      return
		    }
		    parser.tagName = tagName;
		    var s = parser.tags.length;
		    while (s-- > t) {
		      var tag = (parser.tag = parser.tags.pop());
		      parser.tagName = parser.tag.name;
		      emitNode(parser, 'onclosetag', parser.tagName);

		      var x = {};
		      for (var i in tag.ns) {
		        x[i] = tag.ns[i];
		      }

		      var parent = parser.tags[parser.tags.length - 1] || parser;
		      if (parser.opt.xmlns && tag.ns !== parent.ns) {
		        // remove namespace bindings introduced by tag
		        Object.keys(tag.ns).forEach(function (p) {
		          var n = tag.ns[p];
		          emitNode(parser, 'onclosenamespace', { prefix: p, uri: n });
		        });
		      }
		    }
		    if (t === 0) parser.closedRoot = true;
		    parser.tagName = parser.attribValue = parser.attribName = '';
		    parser.attribList.length = 0;
		    parser.state = S.TEXT;
		  }

		  function parseEntity(parser) {
		    var entity = parser.entity;
		    var entityLC = entity.toLowerCase();
		    var num;
		    var numStr = '';

		    if (parser.ENTITIES[entity]) {
		      return parser.ENTITIES[entity]
		    }
		    if (parser.ENTITIES[entityLC]) {
		      return parser.ENTITIES[entityLC]
		    }
		    entity = entityLC;
		    if (entity.charAt(0) === '#') {
		      if (entity.charAt(1) === 'x') {
		        entity = entity.slice(2);
		        num = parseInt(entity, 16);
		        numStr = num.toString(16);
		      } else {
		        entity = entity.slice(1);
		        num = parseInt(entity, 10);
		        numStr = num.toString(10);
		      }
		    }
		    entity = entity.replace(/^0+/, '');
		    if (
		      isNaN(num) ||
		      numStr.toLowerCase() !== entity ||
		      num < 0 ||
		      num > 0x10ffff
		    ) {
		      strictFail(parser, 'Invalid character entity');
		      return '&' + parser.entity + ';'
		    }

		    return String.fromCodePoint(num)
		  }

		  function beginWhiteSpace(parser, c) {
		    if (c === '<') {
		      parser.state = S.OPEN_WAKA;
		      parser.startTagPosition = parser.position;
		    } else if (!isWhitespace(c)) {
		      // have to process this as a text node.
		      // weird, but happens.
		      strictFail(parser, 'Non-whitespace before first tag.');
		      parser.textNode = c;
		      parser.state = S.TEXT;
		    }
		  }

		  function charAt(chunk, i) {
		    var result = '';
		    if (i < chunk.length) {
		      result = chunk.charAt(i);
		    }
		    return result
		  }

		  function write(chunk) {
		    var parser = this;
		    if (this.error) {
		      throw this.error
		    }
		    if (parser.closed) {
		      return error(
		        parser,
		        'Cannot write after close. Assign an onready handler.'
		      )
		    }
		    if (chunk === null) {
		      return end(parser)
		    }
		    if (typeof chunk === 'object') {
		      chunk = chunk.toString();
		    }
		    var i = 0;
		    var c = '';
		    while (true) {
		      c = charAt(chunk, i++);
		      parser.c = c;

		      if (!c) {
		        break
		      }

		      if (parser.trackPosition) {
		        parser.position++;
		        if (c === '\n') {
		          parser.line++;
		          parser.column = 0;
		        } else {
		          parser.column++;
		        }
		      }

		      switch (parser.state) {
		        case S.BEGIN:
		          parser.state = S.BEGIN_WHITESPACE;
		          if (c === '\uFEFF') {
		            continue
		          }
		          beginWhiteSpace(parser, c);
		          continue

		        case S.BEGIN_WHITESPACE:
		          beginWhiteSpace(parser, c);
		          continue

		        case S.TEXT:
		          if (parser.sawRoot && !parser.closedRoot) {
		            var starti = i - 1;
		            while (c && c !== '<' && c !== '&') {
		              c = charAt(chunk, i++);
		              if (c && parser.trackPosition) {
		                parser.position++;
		                if (c === '\n') {
		                  parser.line++;
		                  parser.column = 0;
		                } else {
		                  parser.column++;
		                }
		              }
		            }
		            parser.textNode += chunk.substring(starti, i - 1);
		          }
		          if (
		            c === '<' &&
		            !(parser.sawRoot && parser.closedRoot && !parser.strict)
		          ) {
		            parser.state = S.OPEN_WAKA;
		            parser.startTagPosition = parser.position;
		          } else {
		            if (
		              !isWhitespace(c) &&
		              (!parser.sawRoot || parser.closedRoot)
		            ) {
		              strictFail(parser, 'Text data outside of root node.');
		            }
		            if (c === '&') {
		              parser.state = S.TEXT_ENTITY;
		            } else {
		              parser.textNode += c;
		            }
		          }
		          continue

		        case S.SCRIPT:
		          // only non-strict
		          if (c === '<') {
		            parser.state = S.SCRIPT_ENDING;
		          } else {
		            parser.script += c;
		          }
		          continue

		        case S.SCRIPT_ENDING:
		          if (c === '/') {
		            parser.state = S.CLOSE_TAG;
		          } else {
		            parser.script += '<' + c;
		            parser.state = S.SCRIPT;
		          }
		          continue

		        case S.OPEN_WAKA:
		          // either a /, ?, !, or text is coming next.
		          if (c === '!') {
		            parser.state = S.SGML_DECL;
		            parser.sgmlDecl = '';
		          } else if (isWhitespace(c)) ; else if (isMatch(nameStart, c)) {
		            parser.state = S.OPEN_TAG;
		            parser.tagName = c;
		          } else if (c === '/') {
		            parser.state = S.CLOSE_TAG;
		            parser.tagName = '';
		          } else if (c === '?') {
		            parser.state = S.PROC_INST;
		            parser.procInstName = parser.procInstBody = '';
		          } else {
		            strictFail(parser, 'Unencoded <');
		            // if there was some whitespace, then add that in.
		            if (parser.startTagPosition + 1 < parser.position) {
		              var pad = parser.position - parser.startTagPosition;
		              c = new Array(pad).join(' ') + c;
		            }
		            parser.textNode += '<' + c;
		            parser.state = S.TEXT;
		          }
		          continue

		        case S.SGML_DECL:
		          if (parser.sgmlDecl + c === '--') {
		            parser.state = S.COMMENT;
		            parser.comment = '';
		            parser.sgmlDecl = '';
		            continue
		          }

		          if (
		            parser.doctype &&
		            parser.doctype !== true &&
		            parser.sgmlDecl
		          ) {
		            parser.state = S.DOCTYPE_DTD;
		            parser.doctype += '<!' + parser.sgmlDecl + c;
		            parser.sgmlDecl = '';
		          } else if ((parser.sgmlDecl + c).toUpperCase() === CDATA) {
		            emitNode(parser, 'onopencdata');
		            parser.state = S.CDATA;
		            parser.sgmlDecl = '';
		            parser.cdata = '';
		          } else if ((parser.sgmlDecl + c).toUpperCase() === DOCTYPE) {
		            parser.state = S.DOCTYPE;
		            if (parser.doctype || parser.sawRoot) {
		              strictFail(
		                parser,
		                'Inappropriately located doctype declaration'
		              );
		            }
		            parser.doctype = '';
		            parser.sgmlDecl = '';
		          } else if (c === '>') {
		            emitNode(parser, 'onsgmldeclaration', parser.sgmlDecl);
		            parser.sgmlDecl = '';
		            parser.state = S.TEXT;
		          } else if (isQuote(c)) {
		            parser.state = S.SGML_DECL_QUOTED;
		            parser.sgmlDecl += c;
		          } else {
		            parser.sgmlDecl += c;
		          }
		          continue

		        case S.SGML_DECL_QUOTED:
		          if (c === parser.q) {
		            parser.state = S.SGML_DECL;
		            parser.q = '';
		          }
		          parser.sgmlDecl += c;
		          continue

		        case S.DOCTYPE:
		          if (c === '>') {
		            parser.state = S.TEXT;
		            emitNode(parser, 'ondoctype', parser.doctype);
		            parser.doctype = true; // just remember that we saw it.
		          } else {
		            parser.doctype += c;
		            if (c === '[') {
		              parser.state = S.DOCTYPE_DTD;
		            } else if (isQuote(c)) {
		              parser.state = S.DOCTYPE_QUOTED;
		              parser.q = c;
		            }
		          }
		          continue

		        case S.DOCTYPE_QUOTED:
		          parser.doctype += c;
		          if (c === parser.q) {
		            parser.q = '';
		            parser.state = S.DOCTYPE;
		          }
		          continue

		        case S.DOCTYPE_DTD:
		          if (c === ']') {
		            parser.doctype += c;
		            parser.state = S.DOCTYPE;
		          } else if (c === '<') {
		            parser.state = S.OPEN_WAKA;
		            parser.startTagPosition = parser.position;
		          } else if (isQuote(c)) {
		            parser.doctype += c;
		            parser.state = S.DOCTYPE_DTD_QUOTED;
		            parser.q = c;
		          } else {
		            parser.doctype += c;
		          }
		          continue

		        case S.DOCTYPE_DTD_QUOTED:
		          parser.doctype += c;
		          if (c === parser.q) {
		            parser.state = S.DOCTYPE_DTD;
		            parser.q = '';
		          }
		          continue

		        case S.COMMENT:
		          if (c === '-') {
		            parser.state = S.COMMENT_ENDING;
		          } else {
		            parser.comment += c;
		          }
		          continue

		        case S.COMMENT_ENDING:
		          if (c === '-') {
		            parser.state = S.COMMENT_ENDED;
		            parser.comment = textopts(parser.opt, parser.comment);
		            if (parser.comment) {
		              emitNode(parser, 'oncomment', parser.comment);
		            }
		            parser.comment = '';
		          } else {
		            parser.comment += '-' + c;
		            parser.state = S.COMMENT;
		          }
		          continue

		        case S.COMMENT_ENDED:
		          if (c !== '>') {
		            strictFail(parser, 'Malformed comment');
		            // allow <!-- blah -- bloo --> in non-strict mode,
		            // which is a comment of " blah -- bloo "
		            parser.comment += '--' + c;
		            parser.state = S.COMMENT;
		          } else if (parser.doctype && parser.doctype !== true) {
		            parser.state = S.DOCTYPE_DTD;
		          } else {
		            parser.state = S.TEXT;
		          }
		          continue

		        case S.CDATA:
		          var starti = i - 1;
		          while (c && c !== ']') {
		            c = charAt(chunk, i++);
		            if (c && parser.trackPosition) {
		              parser.position++;
		              if (c === '\n') {
		                parser.line++;
		                parser.column = 0;
		              } else {
		                parser.column++;
		              }
		            }
		          }
		          parser.cdata += chunk.substring(starti, i - 1);
		          if (c === ']') {
		            parser.state = S.CDATA_ENDING;
		          }
		          continue

		        case S.CDATA_ENDING:
		          if (c === ']') {
		            parser.state = S.CDATA_ENDING_2;
		          } else {
		            parser.cdata += ']' + c;
		            parser.state = S.CDATA;
		          }
		          continue

		        case S.CDATA_ENDING_2:
		          if (c === '>') {
		            if (parser.cdata) {
		              emitNode(parser, 'oncdata', parser.cdata);
		            }
		            emitNode(parser, 'onclosecdata');
		            parser.cdata = '';
		            parser.state = S.TEXT;
		          } else if (c === ']') {
		            parser.cdata += ']';
		          } else {
		            parser.cdata += ']]' + c;
		            parser.state = S.CDATA;
		          }
		          continue

		        case S.PROC_INST:
		          if (c === '?') {
		            parser.state = S.PROC_INST_ENDING;
		          } else if (isWhitespace(c)) {
		            parser.state = S.PROC_INST_BODY;
		          } else {
		            parser.procInstName += c;
		          }
		          continue

		        case S.PROC_INST_BODY:
		          if (!parser.procInstBody && isWhitespace(c)) {
		            continue
		          } else if (c === '?') {
		            parser.state = S.PROC_INST_ENDING;
		          } else {
		            parser.procInstBody += c;
		          }
		          continue

		        case S.PROC_INST_ENDING:
		          if (c === '>') {
		            emitNode(parser, 'onprocessinginstruction', {
		              name: parser.procInstName,
		              body: parser.procInstBody,
		            });
		            parser.procInstName = parser.procInstBody = '';
		            parser.state = S.TEXT;
		          } else {
		            parser.procInstBody += '?' + c;
		            parser.state = S.PROC_INST_BODY;
		          }
		          continue

		        case S.OPEN_TAG:
		          if (isMatch(nameBody, c)) {
		            parser.tagName += c;
		          } else {
		            newTag(parser);
		            if (c === '>') {
		              openTag(parser);
		            } else if (c === '/') {
		              parser.state = S.OPEN_TAG_SLASH;
		            } else {
		              if (!isWhitespace(c)) {
		                strictFail(parser, 'Invalid character in tag name');
		              }
		              parser.state = S.ATTRIB;
		            }
		          }
		          continue

		        case S.OPEN_TAG_SLASH:
		          if (c === '>') {
		            openTag(parser, true);
		            closeTag(parser);
		          } else {
		            strictFail(
		              parser,
		              'Forward-slash in opening tag not followed by >'
		            );
		            parser.state = S.ATTRIB;
		          }
		          continue

		        case S.ATTRIB:
		          // haven't read the attribute name yet.
		          if (isWhitespace(c)) {
		            continue
		          } else if (c === '>') {
		            openTag(parser);
		          } else if (c === '/') {
		            parser.state = S.OPEN_TAG_SLASH;
		          } else if (isMatch(nameStart, c)) {
		            parser.attribName = c;
		            parser.attribValue = '';
		            parser.state = S.ATTRIB_NAME;
		          } else {
		            strictFail(parser, 'Invalid attribute name');
		          }
		          continue

		        case S.ATTRIB_NAME:
		          if (c === '=') {
		            parser.state = S.ATTRIB_VALUE;
		          } else if (c === '>') {
		            strictFail(parser, 'Attribute without value');
		            parser.attribValue = parser.attribName;
		            attrib(parser);
		            openTag(parser);
		          } else if (isWhitespace(c)) {
		            parser.state = S.ATTRIB_NAME_SAW_WHITE;
		          } else if (isMatch(nameBody, c)) {
		            parser.attribName += c;
		          } else {
		            strictFail(parser, 'Invalid attribute name');
		          }
		          continue

		        case S.ATTRIB_NAME_SAW_WHITE:
		          if (c === '=') {
		            parser.state = S.ATTRIB_VALUE;
		          } else if (isWhitespace(c)) {
		            continue
		          } else {
		            strictFail(parser, 'Attribute without value');
		            parser.tag.attributes[parser.attribName] = '';
		            parser.attribValue = '';
		            emitNode(parser, 'onattribute', {
		              name: parser.attribName,
		              value: '',
		            });
		            parser.attribName = '';
		            if (c === '>') {
		              openTag(parser);
		            } else if (isMatch(nameStart, c)) {
		              parser.attribName = c;
		              parser.state = S.ATTRIB_NAME;
		            } else {
		              strictFail(parser, 'Invalid attribute name');
		              parser.state = S.ATTRIB;
		            }
		          }
		          continue

		        case S.ATTRIB_VALUE:
		          if (isWhitespace(c)) {
		            continue
		          } else if (isQuote(c)) {
		            parser.q = c;
		            parser.state = S.ATTRIB_VALUE_QUOTED;
		          } else {
		            if (!parser.opt.unquotedAttributeValues) {
		              error(parser, 'Unquoted attribute value');
		            }
		            parser.state = S.ATTRIB_VALUE_UNQUOTED;
		            parser.attribValue = c;
		          }
		          continue

		        case S.ATTRIB_VALUE_QUOTED:
		          if (c !== parser.q) {
		            if (c === '&') {
		              parser.state = S.ATTRIB_VALUE_ENTITY_Q;
		            } else {
		              parser.attribValue += c;
		            }
		            continue
		          }
		          attrib(parser);
		          parser.q = '';
		          parser.state = S.ATTRIB_VALUE_CLOSED;
		          continue

		        case S.ATTRIB_VALUE_CLOSED:
		          if (isWhitespace(c)) {
		            parser.state = S.ATTRIB;
		          } else if (c === '>') {
		            openTag(parser);
		          } else if (c === '/') {
		            parser.state = S.OPEN_TAG_SLASH;
		          } else if (isMatch(nameStart, c)) {
		            strictFail(parser, 'No whitespace between attributes');
		            parser.attribName = c;
		            parser.attribValue = '';
		            parser.state = S.ATTRIB_NAME;
		          } else {
		            strictFail(parser, 'Invalid attribute name');
		          }
		          continue

		        case S.ATTRIB_VALUE_UNQUOTED:
		          if (!isAttribEnd(c)) {
		            if (c === '&') {
		              parser.state = S.ATTRIB_VALUE_ENTITY_U;
		            } else {
		              parser.attribValue += c;
		            }
		            continue
		          }
		          attrib(parser);
		          if (c === '>') {
		            openTag(parser);
		          } else {
		            parser.state = S.ATTRIB;
		          }
		          continue

		        case S.CLOSE_TAG:
		          if (!parser.tagName) {
		            if (isWhitespace(c)) {
		              continue
		            } else if (notMatch(nameStart, c)) {
		              if (parser.script) {
		                parser.script += '</' + c;
		                parser.state = S.SCRIPT;
		              } else {
		                strictFail(parser, 'Invalid tagname in closing tag.');
		              }
		            } else {
		              parser.tagName = c;
		            }
		          } else if (c === '>') {
		            closeTag(parser);
		          } else if (isMatch(nameBody, c)) {
		            parser.tagName += c;
		          } else if (parser.script) {
		            parser.script += '</' + parser.tagName;
		            parser.tagName = '';
		            parser.state = S.SCRIPT;
		          } else {
		            if (!isWhitespace(c)) {
		              strictFail(parser, 'Invalid tagname in closing tag');
		            }
		            parser.state = S.CLOSE_TAG_SAW_WHITE;
		          }
		          continue

		        case S.CLOSE_TAG_SAW_WHITE:
		          if (isWhitespace(c)) {
		            continue
		          }
		          if (c === '>') {
		            closeTag(parser);
		          } else {
		            strictFail(parser, 'Invalid characters in closing tag');
		          }
		          continue

		        case S.TEXT_ENTITY:
		        case S.ATTRIB_VALUE_ENTITY_Q:
		        case S.ATTRIB_VALUE_ENTITY_U:
		          var returnState;
		          var buffer;
		          switch (parser.state) {
		            case S.TEXT_ENTITY:
		              returnState = S.TEXT;
		              buffer = 'textNode';
		              break

		            case S.ATTRIB_VALUE_ENTITY_Q:
		              returnState = S.ATTRIB_VALUE_QUOTED;
		              buffer = 'attribValue';
		              break

		            case S.ATTRIB_VALUE_ENTITY_U:
		              returnState = S.ATTRIB_VALUE_UNQUOTED;
		              buffer = 'attribValue';
		              break
		          }

		          if (c === ';') {
		            var parsedEntity = parseEntity(parser);
		            if (
		              parser.opt.unparsedEntities &&
		              !Object.values(sax.XML_ENTITIES).includes(parsedEntity)
		            ) {
		              parser.entity = '';
		              parser.state = returnState;
		              parser.write(parsedEntity);
		            } else {
		              parser[buffer] += parsedEntity;
		              parser.entity = '';
		              parser.state = returnState;
		            }
		          } else if (
		            isMatch(parser.entity.length ? entityBody : entityStart, c)
		          ) {
		            parser.entity += c;
		          } else {
		            strictFail(parser, 'Invalid character in entity name');
		            parser[buffer] += '&' + parser.entity + c;
		            parser.entity = '';
		            parser.state = returnState;
		          }

		          continue

		        default: /* istanbul ignore next */ {
		          throw new Error(parser, 'Unknown state: ' + parser.state)
		        }
		      }
		    } // while

		    if (parser.position >= parser.bufferCheckPosition) {
		      checkBufferLength(parser);
		    }
		    return parser
		  }

		  /*! http://mths.be/fromcodepoint v0.1.0 by @mathias */
		  /* istanbul ignore next */
		  if (!String.fromCodePoint) {
(function () {
		      var stringFromCharCode = String.fromCharCode;
		      var floor = Math.floor;
		      var fromCodePoint = function () {
		        var MAX_SIZE = 0x4000;
		        var codeUnits = [];
		        var highSurrogate;
		        var lowSurrogate;
		        var index = -1;
		        var length = arguments.length;
		        if (!length) {
		          return ''
		        }
		        var result = '';
		        while (++index < length) {
		          var codePoint = Number(arguments[index]);
		          if (
		            !isFinite(codePoint) || // `NaN`, `+Infinity`, or `-Infinity`
		            codePoint < 0 || // not a valid Unicode code point
		            codePoint > 0x10ffff || // not a valid Unicode code point
		            floor(codePoint) !== codePoint // not an integer
		          ) {
		            throw RangeError('Invalid code point: ' + codePoint)
		          }
		          if (codePoint <= 0xffff) {
		            // BMP code point
		            codeUnits.push(codePoint);
		          } else {
		            // Astral code point; split in surrogate halves
		            // http://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
		            codePoint -= 0x10000;
		            highSurrogate = (codePoint >> 10) + 0xd800;
		            lowSurrogate = (codePoint % 0x400) + 0xdc00;
		            codeUnits.push(highSurrogate, lowSurrogate);
		          }
		          if (index + 1 === length || codeUnits.length > MAX_SIZE) {
		            result += stringFromCharCode.apply(null, codeUnits);
		            codeUnits.length = 0;
		          }
		        }
		        return result
		      };
		      /* istanbul ignore next */
		      if (Object.defineProperty) {
		        Object.defineProperty(String, 'fromCodePoint', {
		          value: fromCodePoint,
		          configurable: true,
		          writable: true,
		        });
		      } else {
		        String.fromCodePoint = fromCodePoint;
		      }
		    })();
		  }
		})(exports); 
	} (sax));
	return sax;
}

var arrayHelper;
var hasRequiredArrayHelper;

function requireArrayHelper () {
	if (hasRequiredArrayHelper) return arrayHelper;
	hasRequiredArrayHelper = 1;
	arrayHelper = {

	  isArray: function(value) {
	    if (Array.isArray) {
	      return Array.isArray(value);
	    }
	    // fallback for older browsers like  IE 8
	    return Object.prototype.toString.call( value ) === '[object Array]';
	  }

	};
	return arrayHelper;
}

var optionsHelper;
var hasRequiredOptionsHelper;

function requireOptionsHelper () {
	if (hasRequiredOptionsHelper) return optionsHelper;
	hasRequiredOptionsHelper = 1;
	var isArray = requireArrayHelper().isArray;

	optionsHelper = {

	  copyOptions: function (options) {
	    var key, copy = {};
	    for (key in options) {
	      if (options.hasOwnProperty(key)) {
	        copy[key] = options[key];
	      }
	    }
	    return copy;
	  },

	  ensureFlagExists: function (item, options) {
	    if (!(item in options) || typeof options[item] !== 'boolean') {
	      options[item] = false;
	    }
	  },

	  ensureSpacesExists: function (options) {
	    if (!('spaces' in options) || (typeof options.spaces !== 'number' && typeof options.spaces !== 'string')) {
	      options.spaces = 0;
	    }
	  },

	  ensureAlwaysArrayExists: function (options) {
	    if (!('alwaysArray' in options) || (typeof options.alwaysArray !== 'boolean' && !isArray(options.alwaysArray))) {
	      options.alwaysArray = false;
	    }
	  },

	  ensureKeyExists: function (key, options) {
	    if (!(key + 'Key' in options) || typeof options[key + 'Key'] !== 'string') {
	      options[key + 'Key'] = options.compact ? '_' + key : key;
	    }
	  },

	  checkFnExists: function (key, options) {
	    return key + 'Fn' in options;
	  }

	};
	return optionsHelper;
}

var xml2js;
var hasRequiredXml2js;

function requireXml2js () {
	if (hasRequiredXml2js) return xml2js;
	hasRequiredXml2js = 1;
	var sax = requireSax();
	var helper = requireOptionsHelper();
	var isArray = requireArrayHelper().isArray;

	var options;
	var currentElement;

	function validateOptions(userOptions) {
	  options = helper.copyOptions(userOptions);
	  helper.ensureFlagExists('ignoreDeclaration', options);
	  helper.ensureFlagExists('ignoreInstruction', options);
	  helper.ensureFlagExists('ignoreAttributes', options);
	  helper.ensureFlagExists('ignoreText', options);
	  helper.ensureFlagExists('ignoreComment', options);
	  helper.ensureFlagExists('ignoreCdata', options);
	  helper.ensureFlagExists('ignoreDoctype', options);
	  helper.ensureFlagExists('compact', options);
	  helper.ensureFlagExists('alwaysChildren', options);
	  helper.ensureFlagExists('addParent', options);
	  helper.ensureFlagExists('trim', options);
	  helper.ensureFlagExists('nativeType', options);
	  helper.ensureFlagExists('nativeTypeAttributes', options);
	  helper.ensureFlagExists('sanitize', options);
	  helper.ensureFlagExists('instructionHasAttributes', options);
	  helper.ensureFlagExists('captureSpacesBetweenElements', options);
	  helper.ensureAlwaysArrayExists(options);
	  helper.ensureKeyExists('declaration', options);
	  helper.ensureKeyExists('instruction', options);
	  helper.ensureKeyExists('attributes', options);
	  helper.ensureKeyExists('text', options);
	  helper.ensureKeyExists('comment', options);
	  helper.ensureKeyExists('cdata', options);
	  helper.ensureKeyExists('doctype', options);
	  helper.ensureKeyExists('type', options);
	  helper.ensureKeyExists('name', options);
	  helper.ensureKeyExists('elements', options);
	  helper.ensureKeyExists('parent', options);
	  helper.checkFnExists('doctype', options);
	  helper.checkFnExists('instruction', options);
	  helper.checkFnExists('cdata', options);
	  helper.checkFnExists('comment', options);
	  helper.checkFnExists('text', options);
	  helper.checkFnExists('instructionName', options);
	  helper.checkFnExists('elementName', options);
	  helper.checkFnExists('attributeName', options);
	  helper.checkFnExists('attributeValue', options);
	  helper.checkFnExists('attributes', options);
	  return options;
	}

	function nativeType(value) {
	  var nValue = Number(value);
	  if (!isNaN(nValue)) {
	    return nValue;
	  }
	  var bValue = value.toLowerCase();
	  if (bValue === 'true') {
	    return true;
	  } else if (bValue === 'false') {
	    return false;
	  }
	  return value;
	}

	function addField(type, value) {
	  var key;
	  if (options.compact) {
	    if (
	      !currentElement[options[type + 'Key']] &&
	      (isArray(options.alwaysArray) ? options.alwaysArray.indexOf(options[type + 'Key']) !== -1 : options.alwaysArray)
	    ) {
	      currentElement[options[type + 'Key']] = [];
	    }
	    if (currentElement[options[type + 'Key']] && !isArray(currentElement[options[type + 'Key']])) {
	      currentElement[options[type + 'Key']] = [currentElement[options[type + 'Key']]];
	    }
	    if (type + 'Fn' in options && typeof value === 'string') {
	      value = options[type + 'Fn'](value, currentElement);
	    }
	    if (type === 'instruction' && ('instructionFn' in options || 'instructionNameFn' in options)) {
	      for (key in value) {
	        if (value.hasOwnProperty(key)) {
	          if ('instructionFn' in options) {
	            value[key] = options.instructionFn(value[key], key, currentElement);
	          } else {
	            var temp = value[key];
	            delete value[key];
	            value[options.instructionNameFn(key, temp, currentElement)] = temp;
	          }
	        }
	      }
	    }
	    if (isArray(currentElement[options[type + 'Key']])) {
	      currentElement[options[type + 'Key']].push(value);
	    } else {
	      currentElement[options[type + 'Key']] = value;
	    }
	  } else {
	    if (!currentElement[options.elementsKey]) {
	      currentElement[options.elementsKey] = [];
	    }
	    var element = {};
	    element[options.typeKey] = type;
	    if (type === 'instruction') {
	      for (key in value) {
	        if (value.hasOwnProperty(key)) {
	          break;
	        }
	      }
	      element[options.nameKey] = 'instructionNameFn' in options ? options.instructionNameFn(key, value, currentElement) : key;
	      if (options.instructionHasAttributes) {
	        element[options.attributesKey] = value[key][options.attributesKey];
	        if ('instructionFn' in options) {
	          element[options.attributesKey] = options.instructionFn(element[options.attributesKey], key, currentElement);
	        }
	      } else {
	        if ('instructionFn' in options) {
	          value[key] = options.instructionFn(value[key], key, currentElement);
	        }
	        element[options.instructionKey] = value[key];
	      }
	    } else {
	      if (type + 'Fn' in options) {
	        value = options[type + 'Fn'](value, currentElement);
	      }
	      element[options[type + 'Key']] = value;
	    }
	    if (options.addParent) {
	      element[options.parentKey] = currentElement;
	    }
	    currentElement[options.elementsKey].push(element);
	  }
	}

	function manipulateAttributes(attributes) {
	  if ('attributesFn' in options && attributes) {
	    attributes = options.attributesFn(attributes, currentElement);
	  }
	  if ((options.trim || 'attributeValueFn' in options || 'attributeNameFn' in options || options.nativeTypeAttributes) && attributes) {
	    var key;
	    for (key in attributes) {
	      if (attributes.hasOwnProperty(key)) {
	        if (options.trim) attributes[key] = attributes[key].trim();
	        if (options.nativeTypeAttributes) {
	          attributes[key] = nativeType(attributes[key]);
	        }
	        if ('attributeValueFn' in options) attributes[key] = options.attributeValueFn(attributes[key], key, currentElement);
	        if ('attributeNameFn' in options) {
	          var temp = attributes[key];
	          delete attributes[key];
	          attributes[options.attributeNameFn(key, attributes[key], currentElement)] = temp;
	        }
	      }
	    }
	  }
	  return attributes;
	}

	function onInstruction(instruction) {
	  var attributes = {};
	  if (instruction.body && (instruction.name.toLowerCase() === 'xml' || options.instructionHasAttributes)) {
	    var attrsRegExp = /([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|(\w+))\s*/g;
	    var match;
	    while ((match = attrsRegExp.exec(instruction.body)) !== null) {
	      attributes[match[1]] = match[2] || match[3] || match[4];
	    }
	    attributes = manipulateAttributes(attributes);
	  }
	  if (instruction.name.toLowerCase() === 'xml') {
	    if (options.ignoreDeclaration) {
	      return;
	    }
	    currentElement[options.declarationKey] = {};
	    if (Object.keys(attributes).length) {
	      currentElement[options.declarationKey][options.attributesKey] = attributes;
	    }
	    if (options.addParent) {
	      currentElement[options.declarationKey][options.parentKey] = currentElement;
	    }
	  } else {
	    if (options.ignoreInstruction) {
	      return;
	    }
	    if (options.trim) {
	      instruction.body = instruction.body.trim();
	    }
	    var value = {};
	    if (options.instructionHasAttributes && Object.keys(attributes).length) {
	      value[instruction.name] = {};
	      value[instruction.name][options.attributesKey] = attributes;
	    } else {
	      value[instruction.name] = instruction.body;
	    }
	    addField('instruction', value);
	  }
	}

	function onStartElement(name, attributes) {
	  var element;
	  if (typeof name === 'object') {
	    attributes = name.attributes;
	    name = name.name;
	  }
	  attributes = manipulateAttributes(attributes);
	  if ('elementNameFn' in options) {
	    name = options.elementNameFn(name, currentElement);
	  }
	  if (options.compact) {
	    element = {};
	    if (!options.ignoreAttributes && attributes && Object.keys(attributes).length) {
	      element[options.attributesKey] = {};
	      var key;
	      for (key in attributes) {
	        if (attributes.hasOwnProperty(key)) {
	          element[options.attributesKey][key] = attributes[key];
	        }
	      }
	    }
	    if (
	      !(name in currentElement) &&
	      (isArray(options.alwaysArray) ? options.alwaysArray.indexOf(name) !== -1 : options.alwaysArray)
	    ) {
	      currentElement[name] = [];
	    }
	    if (currentElement[name] && !isArray(currentElement[name])) {
	      currentElement[name] = [currentElement[name]];
	    }
	    if (isArray(currentElement[name])) {
	      currentElement[name].push(element);
	    } else {
	      currentElement[name] = element;
	    }
	  } else {
	    if (!currentElement[options.elementsKey]) {
	      currentElement[options.elementsKey] = [];
	    }
	    element = {};
	    element[options.typeKey] = 'element';
	    element[options.nameKey] = name;
	    if (!options.ignoreAttributes && attributes && Object.keys(attributes).length) {
	      element[options.attributesKey] = attributes;
	    }
	    if (options.alwaysChildren) {
	      element[options.elementsKey] = [];
	    }
	    currentElement[options.elementsKey].push(element);
	  }
	  element[options.parentKey] = currentElement; // will be deleted in onEndElement() if !options.addParent
	  currentElement = element;
	}

	function onText(text) {
	  if (options.ignoreText) {
	    return;
	  }
	  if (!text.trim() && !options.captureSpacesBetweenElements) {
	    return;
	  }
	  if (options.trim) {
	    text = text.trim();
	  }
	  if (options.nativeType) {
	    text = nativeType(text);
	  }
	  if (options.sanitize) {
	    text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	  }
	  addField('text', text);
	}

	function onComment(comment) {
	  if (options.ignoreComment) {
	    return;
	  }
	  if (options.trim) {
	    comment = comment.trim();
	  }
	  addField('comment', comment);
	}

	function onEndElement(name) {
	  var parentElement = currentElement[options.parentKey];
	  if (!options.addParent) {
	    delete currentElement[options.parentKey];
	  }
	  currentElement = parentElement;
	}

	function onCdata(cdata) {
	  if (options.ignoreCdata) {
	    return;
	  }
	  if (options.trim) {
	    cdata = cdata.trim();
	  }
	  addField('cdata', cdata);
	}

	function onDoctype(doctype) {
	  if (options.ignoreDoctype) {
	    return;
	  }
	  doctype = doctype.replace(/^ /, '');
	  if (options.trim) {
	    doctype = doctype.trim();
	  }
	  addField('doctype', doctype);
	}

	function onError(error) {
	  error.note = error; //console.error(error);
	}

	xml2js = function (xml, userOptions) {

	  var parser = sax.parser(true, {}) ;
	  var result = {};
	  currentElement = result;

	  options = validateOptions(userOptions);

	  {
	    parser.opt = {strictEntities: true};
	    parser.onopentag = onStartElement;
	    parser.ontext = onText;
	    parser.oncomment = onComment;
	    parser.onclosetag = onEndElement;
	    parser.onerror = onError;
	    parser.oncdata = onCdata;
	    parser.ondoctype = onDoctype;
	    parser.onprocessinginstruction = onInstruction;
	  }

	  {
	    parser.write(xml).close();
	  }

	  if (result[options.elementsKey]) {
	    var temp = result[options.elementsKey];
	    delete result[options.elementsKey];
	    result[options.elementsKey] = temp;
	    delete result.text;
	  }

	  return result;

	};
	return xml2js;
}

var xml2json;
var hasRequiredXml2json;

function requireXml2json () {
	if (hasRequiredXml2json) return xml2json;
	hasRequiredXml2json = 1;
	var helper = requireOptionsHelper();
	var xml2js = requireXml2js();

	function validateOptions (userOptions) {
	  var options = helper.copyOptions(userOptions);
	  helper.ensureSpacesExists(options);
	  return options;
	}

	xml2json = function(xml, userOptions) {
	  var options, js, json, parentKey;
	  options = validateOptions(userOptions);
	  js = xml2js(xml, options);
	  parentKey = 'compact' in options && options.compact ? '_parent' : 'parent';
	  // parentKey = ptions.compact ? '_parent' : 'parent'; // consider this
	  if ('addParent' in options && options.addParent) {
	    json = JSON.stringify(js, function (k, v) { return k === parentKey? '_' : v; }, options.spaces);
	  } else {
	    json = JSON.stringify(js, null, options.spaces);
	  }
	  return json.replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
	};
	return xml2json;
}

var js2xml;
var hasRequiredJs2xml;

function requireJs2xml () {
	if (hasRequiredJs2xml) return js2xml;
	hasRequiredJs2xml = 1;
	var helper = requireOptionsHelper();
	var isArray = requireArrayHelper().isArray;

	var currentElement, currentElementName;

	function validateOptions(userOptions) {
	  var options = helper.copyOptions(userOptions);
	  helper.ensureFlagExists('ignoreDeclaration', options);
	  helper.ensureFlagExists('ignoreInstruction', options);
	  helper.ensureFlagExists('ignoreAttributes', options);
	  helper.ensureFlagExists('ignoreText', options);
	  helper.ensureFlagExists('ignoreComment', options);
	  helper.ensureFlagExists('ignoreCdata', options);
	  helper.ensureFlagExists('ignoreDoctype', options);
	  helper.ensureFlagExists('compact', options);
	  helper.ensureFlagExists('indentText', options);
	  helper.ensureFlagExists('indentCdata', options);
	  helper.ensureFlagExists('indentAttributes', options);
	  helper.ensureFlagExists('indentInstruction', options);
	  helper.ensureFlagExists('fullTagEmptyElement', options);
	  helper.ensureFlagExists('noQuotesForNativeAttributes', options);
	  helper.ensureSpacesExists(options);
	  if (typeof options.spaces === 'number') {
	    options.spaces = Array(options.spaces + 1).join(' ');
	  }
	  helper.ensureKeyExists('declaration', options);
	  helper.ensureKeyExists('instruction', options);
	  helper.ensureKeyExists('attributes', options);
	  helper.ensureKeyExists('text', options);
	  helper.ensureKeyExists('comment', options);
	  helper.ensureKeyExists('cdata', options);
	  helper.ensureKeyExists('doctype', options);
	  helper.ensureKeyExists('type', options);
	  helper.ensureKeyExists('name', options);
	  helper.ensureKeyExists('elements', options);
	  helper.checkFnExists('doctype', options);
	  helper.checkFnExists('instruction', options);
	  helper.checkFnExists('cdata', options);
	  helper.checkFnExists('comment', options);
	  helper.checkFnExists('text', options);
	  helper.checkFnExists('instructionName', options);
	  helper.checkFnExists('elementName', options);
	  helper.checkFnExists('attributeName', options);
	  helper.checkFnExists('attributeValue', options);
	  helper.checkFnExists('attributes', options);
	  helper.checkFnExists('fullTagEmptyElement', options);
	  return options;
	}

	function writeIndentation(options, depth, firstLine) {
	  return (!firstLine && options.spaces ? '\n' : '') + Array(depth + 1).join(options.spaces);
	}

	function writeAttributes(attributes, options, depth) {
	  if (options.ignoreAttributes) {
	    return '';
	  }
	  if ('attributesFn' in options) {
	    attributes = options.attributesFn(attributes, currentElementName, currentElement);
	  }
	  var key, attr, attrName, quote, result = [];
	  for (key in attributes) {
	    if (attributes.hasOwnProperty(key) && attributes[key] !== null && attributes[key] !== undefined) {
	      quote = options.noQuotesForNativeAttributes && typeof attributes[key] !== 'string' ? '' : '"';
	      attr = '' + attributes[key]; // ensure number and boolean are converted to String
	      attr = attr.replace(/"/g, '&quot;');
	      attrName = 'attributeNameFn' in options ? options.attributeNameFn(key, attr, currentElementName, currentElement) : key;
	      result.push((options.spaces && options.indentAttributes? writeIndentation(options, depth+1, false) : ' '));
	      result.push(attrName + '=' + quote + ('attributeValueFn' in options ? options.attributeValueFn(attr, key, currentElementName, currentElement) : attr) + quote);
	    }
	  }
	  if (attributes && Object.keys(attributes).length && options.spaces && options.indentAttributes) {
	    result.push(writeIndentation(options, depth, false));
	  }
	  return result.join('');
	}

	function writeDeclaration(declaration, options, depth) {
	  currentElement = declaration;
	  currentElementName = 'xml';
	  return options.ignoreDeclaration ? '' :  '<?' + 'xml' + writeAttributes(declaration[options.attributesKey], options, depth) + '?>';
	}

	function writeInstruction(instruction, options, depth) {
	  if (options.ignoreInstruction) {
	    return '';
	  }
	  var key;
	  for (key in instruction) {
	    if (instruction.hasOwnProperty(key)) {
	      break;
	    }
	  }
	  var instructionName = 'instructionNameFn' in options ? options.instructionNameFn(key, instruction[key], currentElementName, currentElement) : key;
	  if (typeof instruction[key] === 'object') {
	    currentElement = instruction;
	    currentElementName = instructionName;
	    return '<?' + instructionName + writeAttributes(instruction[key][options.attributesKey], options, depth) + '?>';
	  } else {
	    var instructionValue = instruction[key] ? instruction[key] : '';
	    if ('instructionFn' in options) instructionValue = options.instructionFn(instructionValue, key, currentElementName, currentElement);
	    return '<?' + instructionName + (instructionValue ? ' ' + instructionValue : '') + '?>';
	  }
	}

	function writeComment(comment, options) {
	  return options.ignoreComment ? '' : '<!--' + ('commentFn' in options ? options.commentFn(comment, currentElementName, currentElement) : comment) + '-->';
	}

	function writeCdata(cdata, options) {
	  return options.ignoreCdata ? '' : '<![CDATA[' + ('cdataFn' in options ? options.cdataFn(cdata, currentElementName, currentElement) : cdata.replace(']]>', ']]]]><![CDATA[>')) + ']]>';
	}

	function writeDoctype(doctype, options) {
	  return options.ignoreDoctype ? '' : '<!DOCTYPE ' + ('doctypeFn' in options ? options.doctypeFn(doctype, currentElementName, currentElement) : doctype) + '>';
	}

	function writeText(text, options) {
	  if (options.ignoreText) return '';
	  text = '' + text; // ensure Number and Boolean are converted to String
	  text = text.replace(/&amp;/g, '&'); // desanitize to avoid double sanitization
	  text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	  return 'textFn' in options ? options.textFn(text, currentElementName, currentElement) : text;
	}

	function hasContent(element, options) {
	  var i;
	  if (element.elements && element.elements.length) {
	    for (i = 0; i < element.elements.length; ++i) {
	      switch (element.elements[i][options.typeKey]) {
	      case 'text':
	        if (options.indentText) {
	          return true;
	        }
	        break; // skip to next key
	      case 'cdata':
	        if (options.indentCdata) {
	          return true;
	        }
	        break; // skip to next key
	      case 'instruction':
	        if (options.indentInstruction) {
	          return true;
	        }
	        break; // skip to next key
	      case 'doctype':
	      case 'comment':
	      case 'element':
	        return true;
	      default:
	        return true;
	      }
	    }
	  }
	  return false;
	}

	function writeElement(element, options, depth) {
	  currentElement = element;
	  currentElementName = element.name;
	  var xml = [], elementName = 'elementNameFn' in options ? options.elementNameFn(element.name, element) : element.name;
	  xml.push('<' + elementName);
	  if (element[options.attributesKey]) {
	    xml.push(writeAttributes(element[options.attributesKey], options, depth));
	  }
	  var withClosingTag = element[options.elementsKey] && element[options.elementsKey].length || element[options.attributesKey] && element[options.attributesKey]['xml:space'] === 'preserve';
	  if (!withClosingTag) {
	    if ('fullTagEmptyElementFn' in options) {
	      withClosingTag = options.fullTagEmptyElementFn(element.name, element);
	    } else {
	      withClosingTag = options.fullTagEmptyElement;
	    }
	  }
	  if (withClosingTag) {
	    xml.push('>');
	    if (element[options.elementsKey] && element[options.elementsKey].length) {
	      xml.push(writeElements(element[options.elementsKey], options, depth + 1));
	      currentElement = element;
	      currentElementName = element.name;
	    }
	    xml.push(options.spaces && hasContent(element, options) ? '\n' + Array(depth + 1).join(options.spaces) : '');
	    xml.push('</' + elementName + '>');
	  } else {
	    xml.push('/>');
	  }
	  return xml.join('');
	}

	function writeElements(elements, options, depth, firstLine) {
	  return elements.reduce(function (xml, element) {
	    var indent = writeIndentation(options, depth, firstLine && !xml);
	    switch (element.type) {
	    case 'element': return xml + indent + writeElement(element, options, depth);
	    case 'comment': return xml + indent + writeComment(element[options.commentKey], options);
	    case 'doctype': return xml + indent + writeDoctype(element[options.doctypeKey], options);
	    case 'cdata': return xml + (options.indentCdata ? indent : '') + writeCdata(element[options.cdataKey], options);
	    case 'text': return xml + (options.indentText ? indent : '') + writeText(element[options.textKey], options);
	    case 'instruction':
	      var instruction = {};
	      instruction[element[options.nameKey]] = element[options.attributesKey] ? element : element[options.instructionKey];
	      return xml + (options.indentInstruction ? indent : '') + writeInstruction(instruction, options, depth);
	    }
	  }, '');
	}

	function hasContentCompact(element, options, anyContent) {
	  var key;
	  for (key in element) {
	    if (element.hasOwnProperty(key)) {
	      switch (key) {
	      case options.parentKey:
	      case options.attributesKey:
	        break; // skip to next key
	      case options.textKey:
	        if (options.indentText || anyContent) {
	          return true;
	        }
	        break; // skip to next key
	      case options.cdataKey:
	        if (options.indentCdata || anyContent) {
	          return true;
	        }
	        break; // skip to next key
	      case options.instructionKey:
	        if (options.indentInstruction || anyContent) {
	          return true;
	        }
	        break; // skip to next key
	      case options.doctypeKey:
	      case options.commentKey:
	        return true;
	      default:
	        return true;
	      }
	    }
	  }
	  return false;
	}

	function writeElementCompact(element, name, options, depth, indent) {
	  currentElement = element;
	  currentElementName = name;
	  var elementName = 'elementNameFn' in options ? options.elementNameFn(name, element) : name;
	  if (typeof element === 'undefined' || element === null || element === '') {
	    return 'fullTagEmptyElementFn' in options && options.fullTagEmptyElementFn(name, element) || options.fullTagEmptyElement ? '<' + elementName + '></' + elementName + '>' : '<' + elementName + '/>';
	  }
	  var xml = [];
	  if (name) {
	    xml.push('<' + elementName);
	    if (typeof element !== 'object') {
	      xml.push('>' + writeText(element,options) + '</' + elementName + '>');
	      return xml.join('');
	    }
	    if (element[options.attributesKey]) {
	      xml.push(writeAttributes(element[options.attributesKey], options, depth));
	    }
	    var withClosingTag = hasContentCompact(element, options, true) || element[options.attributesKey] && element[options.attributesKey]['xml:space'] === 'preserve';
	    if (!withClosingTag) {
	      if ('fullTagEmptyElementFn' in options) {
	        withClosingTag = options.fullTagEmptyElementFn(name, element);
	      } else {
	        withClosingTag = options.fullTagEmptyElement;
	      }
	    }
	    if (withClosingTag) {
	      xml.push('>');
	    } else {
	      xml.push('/>');
	      return xml.join('');
	    }
	  }
	  xml.push(writeElementsCompact(element, options, depth + 1, false));
	  currentElement = element;
	  currentElementName = name;
	  if (name) {
	    xml.push((indent ? writeIndentation(options, depth, false) : '') + '</' + elementName + '>');
	  }
	  return xml.join('');
	}

	function writeElementsCompact(element, options, depth, firstLine) {
	  var i, key, nodes, xml = [];
	  for (key in element) {
	    if (element.hasOwnProperty(key)) {
	      nodes = isArray(element[key]) ? element[key] : [element[key]];
	      for (i = 0; i < nodes.length; ++i) {
	        switch (key) {
	        case options.declarationKey: xml.push(writeDeclaration(nodes[i], options, depth)); break;
	        case options.instructionKey: xml.push((options.indentInstruction ? writeIndentation(options, depth, firstLine) : '') + writeInstruction(nodes[i], options, depth)); break;
	        case options.attributesKey: case options.parentKey: break; // skip
	        case options.textKey: xml.push((options.indentText ? writeIndentation(options, depth, firstLine) : '') + writeText(nodes[i], options)); break;
	        case options.cdataKey: xml.push((options.indentCdata ? writeIndentation(options, depth, firstLine) : '') + writeCdata(nodes[i], options)); break;
	        case options.doctypeKey: xml.push(writeIndentation(options, depth, firstLine) + writeDoctype(nodes[i], options)); break;
	        case options.commentKey: xml.push(writeIndentation(options, depth, firstLine) + writeComment(nodes[i], options)); break;
	        default: xml.push(writeIndentation(options, depth, firstLine) + writeElementCompact(nodes[i], key, options, depth, hasContentCompact(nodes[i], options)));
	        }
	        firstLine = firstLine && !xml.length;
	      }
	    }
	  }
	  return xml.join('');
	}

	js2xml = function (js, options) {
	  options = validateOptions(options);
	  var xml = [];
	  currentElement = js;
	  currentElementName = '_root_';
	  if (options.compact) {
	    xml.push(writeElementsCompact(js, options, 0, true));
	  } else {
	    if (js[options.declarationKey]) {
	      xml.push(writeDeclaration(js[options.declarationKey], options, 0));
	    }
	    if (js[options.elementsKey] && js[options.elementsKey].length) {
	      xml.push(writeElements(js[options.elementsKey], options, 0, !xml.length));
	    }
	  }
	  return xml.join('');
	};
	return js2xml;
}

var json2xml;
var hasRequiredJson2xml;

function requireJson2xml () {
	if (hasRequiredJson2xml) return json2xml;
	hasRequiredJson2xml = 1;
	var js2xml = requireJs2xml();

	json2xml = function (json, options) {
	  if (json instanceof Buffer) {
	    json = json.toString();
	  }
	  var js = null;
	  if (typeof (json) === 'string') {
	    try {
	      js = JSON.parse(json);
	    } catch (e) {
	      throw new Error('The JSON structure is invalid');
	    }
	  } else {
	    js = json;
	  }
	  return js2xml(js, options);
	};
	return json2xml;
}

/*jslint node:true */

var lib$1z;
var hasRequiredLib$1z;

function requireLib$1z () {
	if (hasRequiredLib$1z) return lib$1z;
	hasRequiredLib$1z = 1;
	var xml2js = requireXml2js();
	var xml2json = requireXml2json();
	var js2xml = requireJs2xml();
	var json2xml = requireJson2xml();

	lib$1z = {
	  xml2js: xml2js,
	  xml2json: xml2json,
	  js2xml: js2xml,
	  json2xml: json2xml
	};
	return lib$1z;
}

var layers$1 = {};

var common = {};

var hasRequiredCommon$2;

function requireCommon$2 () {
	if (hasRequiredCommon$2) return common;
	hasRequiredCommon$2 = 1;
	Object.defineProperty(common, "__esModule", { value: true });
	common.parseRotation = parseRotation;
	common.eagleBoolean = eagleBoolean;
	common.readEagleGrid = readEagleGrid;
	common.addMetadata = addMetadata;
	const Transform_1 = requireTransform();
	const Util_1 = requireUtil();
	function parseRotation(value) {
	    if (!value) {
	        return { rot: 0, mirrored: [false, false] };
	    }
	    if (value.startsWith('R')) {
	        return { rot: (0, Transform_1.clampRotation)(-parseFloat(value.substring(1))), mirrored: [false, false] };
	    }
	    if (value.startsWith('MR')) {
	        return { rot: (0, Transform_1.clampRotation)(-parseFloat(value.substring(2))), mirrored: [false, true] };
	    }
	    return { rot: 0, mirrored: [false, false] };
	}
	function eagleBoolean(value, defaultValue = false) {
	    switch (value?.toLowerCase()) {
	        case 'yes':
	        case 'on':
	            return true;
	        case 'no':
	        case 'off':
	            return false;
	        default:
	            return defaultValue;
	    }
	}
	function readEagleGrid(grid, type) {
	    if (!Util_1.ValidGridUnits.find((u) => u === grid._attributes.unit)) {
	        if (type === 'schematic') {
	            return {
	                distance: 0.1,
	                units: 'inch',
	            };
	        }
	        return {
	            distance: 0.01,
	            units: 'mm',
	        };
	    }
	    return {
	        distance: parseFloat(grid._attributes.distance),
	        units: grid._attributes.unit,
	        multiple: parseInt(grid._attributes.multiple),
	    };
	}
	function addMetadata(obj, key, value) {
	    if (!value) {
	        return;
	    }
	    if (!obj.metadata) {
	        obj.metadata = {};
	    }
	    obj.metadata[key] = value;
	}
	
	return common;
}

var hasRequiredLayers$1;

function requireLayers$1 () {
	if (hasRequiredLayers$1) return layers$1;
	hasRequiredLayers$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.LAYER_EDGE = exports.LAYER_BACK = exports.LAYER_FRONT = exports.EAGLE_SCH_LAYERSET = exports.EAGLE_BRD_LAYERSET = void 0;
		exports.isLayerActive = isLayerActive;
		exports.isLayerVisible = isLayerVisible;
		exports.parsePcbLayer = parsePcbLayer;
		exports.defaultEaglePcbLayers = defaultEaglePcbLayers;
		exports.getEagleDefaultLayerSets = getEagleDefaultLayerSets;
		exports.parseSchLayer = parseSchLayer;
		exports.getEagleSchLayerByType = getEagleSchLayerByType;
		const sigma_engine_shared_1 = requireDist$1();
		const common_1 = requireCommon$2();
		exports.EAGLE_BRD_LAYERSET = 'eaglebrd';
		exports.EAGLE_SCH_LAYERSET = 'eaglesch';
		function isLayerActive(layer) {
		    return (0, common_1.eagleBoolean)(layer._attributes.active);
		}
		function isLayerVisible(layer) {
		    return (0, common_1.eagleBoolean)(layer._attributes.visible);
		}
		function parsePcbLayer(id, name) {
		    const layerProps = PcbLayerMap.find((l) => id === l.id) || { id, type: sigma_engine_shared_1.PcbLayerType.Other };
		    return {
		        ...layerProps,
		        name,
		    };
		}
		function defaultEaglePcbLayers() {
		    return PcbLayerMap.map((l) => ({
		        ...l,
		        name: l.type.toString(),
		    }));
		}
		function getEagleDefaultLayerSets() {
		    return {
		        [exports.EAGLE_SCH_LAYERSET]: {
		            type: 'layerSet',
		            layerSetType: 'schematic',
		            id: exports.EAGLE_SCH_LAYERSET,
		            layers: SchLayerMap,
		        },
		        [exports.EAGLE_BRD_LAYERSET]: {
		            type: 'layerSet',
		            layerSetType: 'pcb',
		            id: exports.EAGLE_BRD_LAYERSET,
		            layers: defaultEaglePcbLayers(),
		        },
		    };
		}
		function parseSchLayer(id, name) {
		    const layerProps = SchLayerMap.find((l) => id === l.id) || { id, type: sigma_engine_shared_1.SchLayerType.Other };
		    return {
		        ...layerProps,
		        name,
		    };
		}
		function getEagleSchLayerByType(layer) {
		    return SchLayerMap.find((l) => l.type === layer);
		}
		exports.LAYER_FRONT = 1;
		exports.LAYER_BACK = 16;
		exports.LAYER_EDGE = 20;
		const PcbLayerMap = [
		    { id: 1, type: sigma_engine_shared_1.PcbLayerType.F, face: 'F', faceMirrorLayer: 16 },
		    { id: 2, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 3, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 4, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 5, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 6, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 7, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 8, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 9, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 10, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 11, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 12, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 13, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 14, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 15, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 16, type: sigma_engine_shared_1.PcbLayerType.B, face: 'B', faceMirrorLayer: 1 },
		    { id: 17, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 18, type: sigma_engine_shared_1.PcbLayerType.Inner },
		    { id: 19, type: sigma_engine_shared_1.PcbLayerType.Other },
		    { id: 20, type: sigma_engine_shared_1.PcbLayerType.Edge },
		    { id: 21, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'F', faceMirrorLayer: 22 },
		    { id: 22, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'B', faceMirrorLayer: 21 },
		    { id: 23, type: sigma_engine_shared_1.PcbLayerType.Other, face: 'F', faceMirrorLayer: 24 },
		    { id: 24, type: sigma_engine_shared_1.PcbLayerType.Other, face: 'B', faceMirrorLayer: 23 },
		    { id: 25, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'F', faceMirrorLayer: 26 },
		    { id: 26, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'B', faceMirrorLayer: 25 },
		    { id: 27, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'F', faceMirrorLayer: 28 },
		    { id: 28, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'B', faceMirrorLayer: 27 },
		    { id: 29, type: sigma_engine_shared_1.PcbLayerType.Mask, face: 'F', faceMirrorLayer: 30 },
		    { id: 30, type: sigma_engine_shared_1.PcbLayerType.Mask, face: 'B', faceMirrorLayer: 29 },
		    { id: 31, type: sigma_engine_shared_1.PcbLayerType.Paste, face: 'F', faceMirrorLayer: 32 },
		    { id: 32, type: sigma_engine_shared_1.PcbLayerType.Paste, face: 'B', faceMirrorLayer: 31 },
		    { id: 51, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'F', faceMirrorLayer: 52 },
		    { id: 52, type: sigma_engine_shared_1.PcbLayerType.SilkS, face: 'B', faceMirrorLayer: 51 },
		];
		const SchLayerMap = [
		    { id: 91, name: sigma_engine_shared_1.SchLayerType.Wire.toString(), type: sigma_engine_shared_1.SchLayerType.Wire },
		    { id: 92, name: sigma_engine_shared_1.SchLayerType.Bus.toString(), type: sigma_engine_shared_1.SchLayerType.Bus },
		    { id: 93, name: sigma_engine_shared_1.SchLayerType.Pin.toString(), type: sigma_engine_shared_1.SchLayerType.Pin, defaultVisible: true },
		    { id: 94, name: sigma_engine_shared_1.SchLayerType.Symbol.toString(), type: sigma_engine_shared_1.SchLayerType.Symbol },
		    { id: 95, name: sigma_engine_shared_1.SchLayerType.NameLabel.toString(), type: sigma_engine_shared_1.SchLayerType.NameLabel },
		    { id: 96, name: sigma_engine_shared_1.SchLayerType.ValueLabel.toString(), type: sigma_engine_shared_1.SchLayerType.ValueLabel },
		    { id: 97, name: sigma_engine_shared_1.SchLayerType.Label.toString(), type: sigma_engine_shared_1.SchLayerType.Label },
		    { id: 98, name: sigma_engine_shared_1.SchLayerType.Label.toString(), type: sigma_engine_shared_1.SchLayerType.Label },
		    { id: 104, name: sigma_engine_shared_1.SchLayerType.NameLabel.toString(), type: sigma_engine_shared_1.SchLayerType.NameLabel },
		    { id: 104, name: sigma_engine_shared_1.SchLayerType.NameLabel.toString(), type: sigma_engine_shared_1.SchLayerType.PinName },
		    { id: 104, name: sigma_engine_shared_1.SchLayerType.NameLabel.toString(), type: sigma_engine_shared_1.SchLayerType.PinNumber },
		    { id: 150, name: sigma_engine_shared_1.SchLayerType.Label.toString(), type: sigma_engine_shared_1.SchLayerType.Label },
		    { id: 256, name: sigma_engine_shared_1.SchLayerType.Junction.toString(), type: sigma_engine_shared_1.SchLayerType.Junction },
		];
		
	} (layers$1));
	return layers$1;
}

var Common$1 = {};

var Board = {};

var Label = {};

var Point$4 = {};

var Rotation = {};

var hasRequiredRotation;

function requireRotation () {
	if (hasRequiredRotation) return Rotation;
	hasRequiredRotation = 1;
	Object.defineProperty(Rotation, "__esModule", { value: true });
	Rotation.Rotation = void 0;
	Rotation.combineRotations = combineRotations;
	Rotation.parseRotationFromDegrees = parseRotationFromDegrees;
	Rotation.convertToTransform = convertToTransform;
	var Rotation$1;
	(function (Rotation) {
	    Rotation[Rotation["R0"] = 0] = "R0";
	    Rotation[Rotation["R90"] = 90] = "R90";
	    Rotation[Rotation["R180"] = 180] = "R180";
	    Rotation[Rotation["R270"] = 270] = "R270";
	})(Rotation$1 || (Rotation.Rotation = Rotation$1 = {}));
	function combineRotations(rot1, rot2) {
	    const result = rot1 + rot2;
	    return parseRotationFromDegrees(result);
	}
	function parseRotationFromDegrees(rot) {
	    if (!rot) {
	        return Rotation$1.R0;
	    }
	    if (typeof rot === 'string') {
	        switch (rot) {
	            case 'R270':
	                return Rotation$1.R270;
	            case 'R180':
	                return Rotation$1.R180;
	            case 'R90':
	                return Rotation$1.R90;
	            default:
	                return Rotation$1.R0;
	        }
	    }
	    while (rot > 360) {
	        rot -= 360;
	    }
	    switch (rot) {
	        case 270:
	            return Rotation$1.R270;
	        case 180:
	            return Rotation$1.R180;
	        case 90:
	            return Rotation$1.R90;
	        default:
	            return Rotation$1.R0;
	    }
	}
	function convertToTransform(r, mirrored) {
	    switch (r) {
	        case Rotation$1.R270:
	            return { rot: 270, sx: 1, sy: mirrored ? -1 : 1 };
	        case Rotation$1.R180:
	            return { rot: 180, sx: mirrored ? -1 : 1, sy: 1 };
	        case Rotation$1.R90:
	            return { rot: 90, sx: 1, sy: mirrored ? -1 : 1 };
	        default:
	            return { rot: 0, sx: mirrored ? -1 : 1, sy: 1 };
	    }
	}
	
	return Rotation;
}

var hasRequiredPoint;

function requirePoint () {
	if (hasRequiredPoint) return Point$4;
	hasRequiredPoint = 1;
	Object.defineProperty(Point$4, "__esModule", { value: true });
	Point$4.Point = void 0;
	const Rotation_1 = requireRotation();
	class Point {
	    static fromPoint(p) {
	        return new Point(p.x, p.y);
	    }
	    constructor(x, y) {
	        this.x = typeof x === 'string' ? parseFloat(x) : x;
	        this.y = typeof y === 'string' ? parseFloat(y) : y;
	    }
	    rotate(rot) {
	        let p = Point.fromPoint(this);
	        if (rot === Rotation_1.Rotation.R0) {
	            return p;
	        }
	        p = new Point(p.y, -p.x);
	        if (rot === Rotation_1.Rotation.R270) {
	            return p;
	        }
	        p = new Point(p.y, -p.x);
	        if (rot === Rotation_1.Rotation.R180) {
	            return p;
	        }
	        p = new Point(p.y, -p.x);
	        return p;
	    }
	    add(p) {
	        this.x += p.x;
	        this.y += p.y;
	    }
	    toXYPosition() {
	        return [this.x, this.y];
	    }
	}
	Point$4.Point = Point;
	
	return Point$4;
}

var hasRequiredLabel;

function requireLabel () {
	if (hasRequiredLabel) return Label;
	hasRequiredLabel = 1;
	Object.defineProperty(Label, "__esModule", { value: true });
	Label.Label = Label.Anchor = Label.TextAnchor = void 0;
	const Area_1 = requireArea();
	const Point_1 = requirePoint();
	const Rotation_1 = requireRotation();
	var TextAnchor;
	(function (TextAnchor) {
	    TextAnchor["Start"] = "start";
	    TextAnchor["End"] = "end";
	})(TextAnchor || (Label.TextAnchor = TextAnchor = {}));
	var Anchor;
	(function (Anchor) {
	    Anchor[Anchor["TopRight"] = 0] = "TopRight";
	    Anchor[Anchor["TopLeft"] = 1] = "TopLeft";
	    Anchor[Anchor["BottomLeft"] = 2] = "BottomLeft";
	    Anchor[Anchor["BottomRight"] = 3] = "BottomRight";
	    Anchor[Anchor["MiddleLeft"] = 4] = "MiddleLeft";
	    Anchor[Anchor["MiddleRight"] = 5] = "MiddleRight";
	})(Anchor || (Label.Anchor = Anchor = {}));
	class Label$1 {
	    static fromEagleAttribute(t, element) {
	        let point = new Point_1.Point(t._attributes.x, t._attributes.y);
	        let rot = t._attributes.rot;
	        let mirrored = false;
	        if (rot?.startsWith('M')) {
	            mirrored = true;
	            rot = rot.substr(1);
	        }
	        let rotation = (0, Rotation_1.parseRotationFromDegrees)(rot);
	        let { anchor, drawDownXAxis } = Label$1.getDrawDirection(rotation);
	        let value = t._attributes.value;
	        if (!value && 'NAME' === t._attributes.name) {
	            value = element._attributes.name;
	        }
	        const result = new Label$1(point, parseFloat(t._attributes.size), parseInt(t._attributes.layer), value, drawDownXAxis, anchor, mirrored);
	        result.hidden = 'off' === t._attributes.display;
	        return result;
	    }
	    static fromEagleText(t, parent) {
	        let point = new Point_1.Point(t._attributes.x, t._attributes.y);
	        let rot = t._attributes.rot;
	        let mirrored = false;
	        if (rot?.startsWith('M')) {
	            mirrored = true;
	            rot = rot.substr(1);
	        }
	        let rotation = (0, Rotation_1.parseRotationFromDegrees)(rot);
	        if (parent) {
	            point = point.rotate(parent.rotation);
	            point.add(parent.point);
	            rotation = (0, Rotation_1.combineRotations)(rotation, parent.rotation);
	        }
	        let { anchor, drawDownXAxis } = Label$1.getDrawDirection(rotation);
	        return new Label$1(point, parseFloat(t._attributes.size), parseInt(t._attributes.layer), t._text, drawDownXAxis, anchor, mirrored);
	    }
	    constructor(p, size, layer, value, drawDownXAxis, anchor, mirrored) {
	        this.point = p;
	        this.size = size;
	        this.layer = layer;
	        this.value = value;
	        this.drawDownXAxis = drawDownXAxis;
	        this.anchor = anchor;
	        this.mirrored = mirrored;
	        this.dominantBaseline = this.getDominantBaseline();
	        this.anchorText = this.getTextAnchor();
	    }
	    getArea() {
	        let a = new Area_1.default();
	        a.extend([this.point.x, this.point.y]);
	        return a;
	    }
	    getDominantBaseline() {
	        switch (this.anchor) {
	            case Anchor.MiddleRight:
	            case Anchor.MiddleLeft:
	                return 'middle';
	            case Anchor.TopRight:
	            case Anchor.TopLeft:
	                return 'hanging';
	            case Anchor.BottomLeft:
	            case Anchor.BottomRight:
	            default:
	                return 'baseline';
	        }
	    }
	    getTextAnchor() {
	        switch (this.anchor) {
	            case Anchor.BottomRight:
	            case Anchor.MiddleRight:
	            case Anchor.TopRight:
	                return 'end';
	            case Anchor.TopLeft:
	            case Anchor.MiddleLeft:
	            case Anchor.BottomLeft:
	            default:
	                return 'start';
	        }
	    }
	    static getDrawDirection(rotation) {
	        let drawDownXAxis = true;
	        let anchor;
	        switch (rotation) {
	            case Rotation_1.Rotation.R90:
	                anchor = Anchor.BottomLeft;
	                drawDownXAxis = false;
	                break;
	            case Rotation_1.Rotation.R180:
	                anchor = Anchor.TopRight;
	                drawDownXAxis = true;
	                break;
	            case Rotation_1.Rotation.R270:
	                anchor = Anchor.TopRight;
	                drawDownXAxis = false;
	                break;
	            case Rotation_1.Rotation.R0:
	            default:
	                anchor = Anchor.BottomLeft;
	                drawDownXAxis = true;
	                break;
	        }
	        return { anchor, drawDownXAxis };
	    }
	}
	Label.Label = Label$1;
	
	return Label;
}

var Rectangle = {};

var hasRequiredRectangle;

function requireRectangle () {
	if (hasRequiredRectangle) return Rectangle;
	hasRequiredRectangle = 1;
	Object.defineProperty(Rectangle, "__esModule", { value: true });
	Rectangle.Rectangle = void 0;
	const Point_1 = requirePoint();
	const Rotation_1 = requireRotation();
	class Rectangle$1 {
	    static createFromEagle(rect, parent) {
	        let p = new Point_1.Point(rect._attributes.x1, rect._attributes.y1);
	        let width = parseFloat(rect._attributes.x2) - parseFloat(rect._attributes.x1);
	        let height = parseFloat(rect._attributes.y2) - parseFloat(rect._attributes.y1);
	        if (parent) {
	            p = p.rotate(parent.rotation);
	            p.add(parent.point);
	            if ([Rotation_1.Rotation.R90, Rotation_1.Rotation.R270].includes(parent.rotation)) {
	                let temp = width;
	                width = height;
	                height = temp;
	            }
	        }
	        return new Rectangle$1(p.x, p.y, width, height, parseInt(rect._attributes.layer));
	    }
	    constructor(x, y, width, height, layer) {
	        this.point = new Point_1.Point(x, y);
	        this.width = width;
	        this.height = height;
	        this.layer = layer;
	    }
	}
	Rectangle.Rectangle = Rectangle$1;
	
	return Rectangle;
}

var Wire = {};

var hasRequiredWire;

function requireWire () {
	if (hasRequiredWire) return Wire;
	hasRequiredWire = 1;
	Object.defineProperty(Wire, "__esModule", { value: true });
	Wire.Wire = void 0;
	const Point_1 = requirePoint();
	class Wire$1 {
	    static createFromEagle(w, parent) {
	        const wire = new Wire$1();
	        wire.layer = parseInt(w._attributes.layer);
	        wire.width = parseFloat(w._attributes.width);
	        let p1 = new Point_1.Point(w._attributes.x1, w._attributes.y1);
	        let p2 = new Point_1.Point(w._attributes.x2, w._attributes.y2);
	        if (parent) {
	            p1 = p1.rotate(parent.rotation);
	            p2 = p2.rotate(parent.rotation);
	            if (parent.mirrored) {
	                p1.x = -p1.x;
	                p2.x = -p2.x;
	            }
	            p1.add(parent.point);
	            p2.add(parent.point);
	        }
	        wire.vertices = [p1, p2];
	        return wire;
	    }
	    combine(w) {
	        return false;
	    }
	}
	Wire.Wire = Wire$1;
	
	return Wire;
}

var Polygon$2 = {};

var hasRequiredPolygon;

function requirePolygon () {
	if (hasRequiredPolygon) return Polygon$2;
	hasRequiredPolygon = 1;
	Object.defineProperty(Polygon$2, "__esModule", { value: true });
	Polygon$2.Polygon = void 0;
	const Point_1 = requirePoint();
	class Polygon {
	    constructor(p, parent) {
	        this.layer = parseInt(p._attributes.layer);
	        this.spacing = p._attributes.spacing ? parseFloat(p._attributes.spacing) : undefined;
	        this.width = parseFloat(p._attributes.width);
	        this.vertices = p.vertex.map((v) => new Point_1.Point(v._attributes.x, v._attributes.y));
	        if (parent) {
	            this.parentName = parent.name;
	            this.vertices = this.vertices.map((v) => {
	                v = v.rotate(parent.rotation);
	                if (parent.mirrored) {
	                    v.x = -v.x;
	                }
	                v.add(parent.point);
	                return v;
	            });
	        }
	    }
	}
	Polygon$2.Polygon = Polygon;
	
	return Polygon$2;
}

var Common = {};

var hasRequiredCommon$1;

function requireCommon$1 () {
	if (hasRequiredCommon$1) return Common;
	hasRequiredCommon$1 = 1;
	Object.defineProperty(Common, "__esModule", { value: true });
	Common.Circle = Common.Grid = Common.Metadata = void 0;
	Common.toArray = toArray;
	const Area_1 = requireArea();
	const Point_1 = requirePoint();
	function toArray(obj) {
	    if (obj === null || obj === undefined) {
	        return [];
	    }
	    return Array.isArray(obj) ? obj : [obj];
	}
	class Metadata {
	    constructor() {
	        this.parserVersion = '0.0.1';
	        this.boundary = new Area_1.default();
	    }
	    static createFromSchematic(sch) {
	        const metadata = new Metadata();
	        metadata.cadTool = 'eagle';
	        metadata.cadToolVersion = sch._attributes.version;
	        return metadata;
	    }
	    static createFromBoard(brd) {
	        const metadata = new Metadata();
	        metadata.cadTool = 'eagle';
	        metadata.cadToolVersion = brd._attributes.version;
	        return metadata;
	    }
	}
	Common.Metadata = Metadata;
	class Grid {
	    constructor(g) {
	        this.distance = parseInt(g._attributes.distance);
	        this.altDistance = parseInt(g._attributes.altdistance);
	    }
	}
	Common.Grid = Grid;
	class Circle {
	    static createFromEagleSchematic(c, parent) {
	        const circle = new Circle();
	        circle.point = new Point_1.Point(c._attributes.x, c._attributes.y);
	        circle.radius = parseFloat(c._attributes.radius);
	        circle.width = parseFloat(c._attributes.width);
	        circle.layer = parseInt(c._attributes.layer);
	        circle.point = circle.point.rotate(parent.rotation);
	        if (parent.mirrored) {
	            circle.point.x = -circle.point.x;
	        }
	        circle.point.add(parent.point);
	        return circle;
	    }
	    getArea() {
	        const area = new Area_1.default();
	        area.extend([this.point.x - this.radius, this.point.y - this.radius]);
	        area.extend([this.point.x + this.radius, this.point.y + this.radius]);
	        return area;
	    }
	}
	Common.Circle = Circle;
	
	return Common;
}

var hasRequiredBoard$1;

function requireBoard$1 () {
	if (hasRequiredBoard$1) return Board;
	hasRequiredBoard$1 = 1;
	Object.defineProperty(Board, "__esModule", { value: true });
	Board.Hole = Board.Via = Board.ViaShape = Board.Pad = Board.PadShape = Board.Smd = Board.ElementEntity = Board.Board = Board.BoardLayer = void 0;
	Board.parseBoardXml = parseBoardXml;
	const xml_js_1 = requireLib$1z();
	const Label_1 = requireLabel();
	const Rectangle_1 = requireRectangle();
	const Wire_1 = requireWire();
	const Polygon_1 = requirePolygon();
	const Area_1 = requireArea();
	const Point_1 = requirePoint();
	const Rotation_1 = requireRotation();
	const Common_1 = requireCommon$1();
	function parseBoardXml(input) {
	    const json = (0, xml_js_1.xml2json)(input, { compact: true });
	    const root = JSON.parse(json);
	    return new Board$1(root.eagle);
	}
	class BoardLayer {
	    constructor(layer) {
	        this.wires = [];
	        this.rectangles = [];
	        this.vias = [];
	        this.polygons = [];
	        this.pads = [];
	        this.smds = [];
	        this.holes = [];
	        this.labels = [];
	        this.name = layer._attributes.name;
	        this.visible = 'yes' === layer._attributes.visible;
	        this.color = parseInt(layer._attributes.color);
	        this.number = parseInt(layer._attributes.number);
	        this.fill = parseInt(layer._attributes.fill);
	    }
	}
	Board.BoardLayer = BoardLayer;
	class Board$1 {
	    constructor(brd) {
	        this.metadata = Common_1.Metadata.createFromBoard(brd);
	        this.grid = new Common_1.Grid(brd.drawing.grid);
	        this.layers = {};
	        const boundary = new Area_1.default();
	        let packages = {};
	        packages = collectPackages(brd);
	        brd.drawing.layers.layer.forEach((l) => (this.layers[l._attributes.number] = new BoardLayer(l)));
	        (0, Common_1.toArray)(brd.drawing.board.elements.element).forEach((e) => {
	            const element = new ElementEntity(e);
	            const eaglePackage = selectPackage(packages, e._attributes.library, e._attributes.package);
	            let pad;
	            (0, Common_1.toArray)(eaglePackage.pad).forEach((p) => {
	                pad = new Pad(p, element);
	                this.layers[pad.layer].pads.push(pad);
	                boundary.extendWithDimensions(pad.point.x, pad.point.y, pad.diameter ?? pad.drillDiameter, pad.diameter ?? pad.drillDiameter);
	            });
	            const wires = collectWires((0, Common_1.toArray)(eaglePackage.wire), element);
	            Object.keys(wires).forEach((key) => {
	                wires[key].forEach((wire) => {
	                    this.layers[key].wires.push(wire);
	                    boundary.extendWithVertices(wire.vertices.map((v) => v.toXYPosition()));
	                });
	            });
	            let smd;
	            (0, Common_1.toArray)(eaglePackage.smd).forEach((s) => {
	                smd = new Smd(s, element);
	                this.layers[smd.layer].smds.push(smd);
	                boundary.extendWithDimensions(smd.point.x, smd.point.y, smd.width, smd.height);
	            });
	            let polygon;
	            (0, Common_1.toArray)(eaglePackage.polygon).forEach((p) => {
	                polygon = new Polygon_1.Polygon(p, element);
	                this.layers[polygon.layer].polygons.push(polygon);
	                boundary.extendWithVertices(polygon.vertices.map((v) => v.toXYPosition()));
	            });
	            let rect;
	            (0, Common_1.toArray)(eaglePackage.rectangle).forEach((r) => {
	                rect = Rectangle_1.Rectangle.createFromEagle(r, element);
	                this.layers[rect.layer].rectangles.push(rect);
	                boundary.extendWithDimensions(rect.point.x, rect.point.y, rect.width, rect.height);
	            });
	            let label;
	            (0, Common_1.toArray)(e.attribute).forEach((t) => {
	                label = Label_1.Label.fromEagleAttribute(t, e);
	                if (label.hidden) {
	                    return;
	                }
	                this.layers[label.layer].labels.push(label);
	                boundary.extendWithArea(label.getArea());
	            });
	            (0, Common_1.toArray)(eaglePackage.text).forEach((t) => {
	                label = Label_1.Label.fromEagleText(t, element);
	                this.layers[label.layer].labels.push(label);
	                boundary.extendWithArea(label.getArea());
	            });
	        });
	        let wire;
	        (0, Common_1.toArray)(brd.drawing.board.plain.wire)?.forEach((w) => {
	            wire = Wire_1.Wire.createFromEagle(w);
	            this.layers[wire.layer].wires.push(wire);
	            boundary.extendWithVertices(wire.vertices.map((v) => v.toXYPosition()));
	        });
	        let rectangle;
	        (0, Common_1.toArray)(brd.drawing.board.plain.rectangle)?.forEach((r) => {
	            rectangle = Rectangle_1.Rectangle.createFromEagle(r);
	            this.layers[rectangle.layer].rectangles.push(rectangle);
	            boundary.extendWithDimensions(rectangle.point.x, rectangle.point.y, rectangle.width, rectangle.height);
	        });
	        let label;
	        (0, Common_1.toArray)(brd.drawing.board.plain.text)?.forEach((t) => {
	            label = Label_1.Label.fromEagleText(t);
	            this.layers[label.layer].labels.push(label);
	            boundary.extendWithArea(label.getArea());
	        });
	        let hole;
	        (0, Common_1.toArray)(brd.drawing.board.plain.hole)?.forEach((h) => {
	            hole = new Hole(h);
	            this.layers[hole.layer].holes.push(hole);
	            boundary.extendWithDimensions(hole.point.x, hole.point.y, hole.diameter, hole.diameter);
	        });
	        (0, Common_1.toArray)(brd.drawing.board.signals.signal)?.forEach((s) => {
	            const wires = collectWires((0, Common_1.toArray)(s.wire));
	            Object.keys(wires).forEach((key) => {
	                wires[key].forEach((wire) => {
	                    this.layers[key].wires.push(wire);
	                    boundary.extendWithVertices(wire.vertices.map((v) => v.toXYPosition()));
	                });
	            });
	            let via;
	            (0, Common_1.toArray)(s.via)?.forEach((v) => {
	                via = new Via(v);
	                this.layers[via.layer].vias.push(via);
	                boundary.extendWithDimensions(via.point.x, via.point.y, via.diameter, via.diameter);
	            });
	            let polygon;
	            (0, Common_1.toArray)(s.polygon).forEach((p) => {
	                polygon = new Polygon_1.Polygon(p);
	                this.layers[polygon.layer].polygons.push(polygon);
	                boundary.extendWithVertices(polygon.vertices.map((v) => v.toXYPosition()));
	            });
	        });
	        this.metadata.boundary = boundary;
	    }
	}
	Board.Board = Board$1;
	class ElementEntity {
	    constructor(e) {
	        let rot = e._attributes.rot;
	        this.point = new Point_1.Point(e._attributes.x, e._attributes.y);
	        this.name = e._attributes.name;
	        if (rot?.startsWith('M')) {
	            this.mirrored = true;
	            rot = rot.substr(1);
	        }
	        this.rotation = (0, Rotation_1.parseRotationFromDegrees)(rot);
	    }
	}
	Board.ElementEntity = ElementEntity;
	class Smd {
	    constructor(s, parent) {
	        let rot;
	        this.name = s._attributes.name;
	        this.parentName = parent.name;
	        this.layer = parseInt(s._attributes.layer);
	        this.width = parseFloat(s._attributes.dx);
	        this.height = parseFloat(s._attributes.dy);
	        this.roundness = s._attributes.roundness ? parseFloat(s._attributes.roundness) : 0;
	        this.point = new Point_1.Point(parseFloat(s._attributes.x), parseFloat(s._attributes.y));
	        this.point = this.point.rotate(parent.rotation);
	        if (parent.mirrored) {
	            this.point.x = -this.point.x;
	        }
	        rot = (0, Rotation_1.parseRotationFromDegrees)(s._attributes.rot);
	        rot = (0, Rotation_1.combineRotations)(rot, parent.rotation);
	        if ([Rotation_1.Rotation.R90, Rotation_1.Rotation.R270].includes(rot)) {
	            const tmp = this.width;
	            this.width = this.height;
	            this.height = tmp;
	        }
	        this.point.x -= this.width / 2;
	        this.point.y -= this.height / 2;
	        this.point.add(parent.point);
	    }
	}
	Board.Smd = Smd;
	var PadShape;
	(function (PadShape) {
	    PadShape[PadShape["octagon"] = 0] = "octagon";
	    PadShape[PadShape["long"] = 1] = "long";
	    PadShape[PadShape["offset"] = 2] = "offset";
	    PadShape[PadShape["square"] = 3] = "square";
	    PadShape[PadShape["round"] = 4] = "round";
	})(PadShape || (Board.PadShape = PadShape = {}));
	class Pad {
	    constructor(p, parent) {
	        this.drawDirection = parent.rotation;
	        this.layer = 17;
	        this.point = new Point_1.Point(p._attributes.x, p._attributes.y);
	        this.drillDiameter = parseFloat(p._attributes.drill);
	        this.name = p._attributes.name;
	        this.parentName = parent.name;
	        this.diameter = p._attributes.diameter ? parseFloat(p._attributes.diameter) : undefined;
	        this.shape = (() => {
	            switch (p._attributes.shape) {
	                case 'octagon':
	                    return PadShape.octagon;
	                case 'long':
	                    return PadShape.long;
	                case 'offset':
	                    return PadShape.offset;
	                case 'square':
	                    return PadShape.square;
	                case 'round':
	                default:
	                    return PadShape.round;
	            }
	        })();
	        this.drawDirection = (0, Rotation_1.parseRotationFromDegrees)(p._attributes.rot);
	        this.drawDirection = (0, Rotation_1.combineRotations)(this.drawDirection, parent.rotation);
	        this.point = this.point.rotate(parent.rotation);
	        if (parent.mirrored) {
	            this.point.x = -this.point.x;
	        }
	        this.point.add(parent.point);
	    }
	}
	Board.Pad = Pad;
	var ViaShape;
	(function (ViaShape) {
	    ViaShape[ViaShape["octagon"] = 0] = "octagon";
	    ViaShape[ViaShape["square"] = 1] = "square";
	    ViaShape[ViaShape["round"] = 2] = "round";
	})(ViaShape || (Board.ViaShape = ViaShape = {}));
	class Via {
	    constructor(eagleVia) {
	        this.point = new Point_1.Point(eagleVia._attributes.x, eagleVia._attributes.y);
	        this.drillDiameter = parseFloat(eagleVia._attributes.drill);
	        this.layer = 18;
	        this.diameter = this.drillDiameter * 2;
	        switch (eagleVia._attributes.shape) {
	            case 'octagon':
	                this.shape = ViaShape.octagon;
	                break;
	            case 'square':
	                this.shape = ViaShape.square;
	                break;
	            case 'round':
	            default:
	                this.shape = ViaShape.round;
	                break;
	        }
	    }
	}
	Board.Via = Via;
	class Hole {
	    constructor(h) {
	        this.point = new Point_1.Point(h._attributes.x, h._attributes.y);
	        this.diameter = parseFloat(h._attributes.drill);
	        this.layer = 45;
	    }
	}
	Board.Hole = Hole;
	function collectWires(eagleWires, parent) {
	    const result = {};
	    let wire;
	    let prevWire = undefined;
	    if (!eagleWires) {
	        return {};
	    }
	    for (let i = 0; i < eagleWires.length; i++) {
	        wire = Wire_1.Wire.createFromEagle(eagleWires[i], parent);
	        if (!prevWire) {
	            prevWire = wire;
	        }
	        else if (!prevWire.combine(wire)) {
	            if (!result[prevWire.layer]) {
	                result[prevWire.layer] = [];
	            }
	            result[prevWire.layer].push(prevWire);
	            prevWire = wire;
	        }
	    }
	    if (prevWire) {
	        if (!result[prevWire.layer]) {
	            result[prevWire.layer] = [];
	        }
	        result[prevWire.layer].push(prevWire);
	    }
	    return result;
	}
	function collectPackages(brd) {
	    const packages = {};
	    (0, Common_1.toArray)(brd.drawing.board.libraries.library).forEach((l) => {
	        (0, Common_1.toArray)(l.packages.package).forEach((p) => {
	            packages[`${l._attributes.name}::${p._attributes.name}`] = p;
	        });
	    });
	    return packages;
	}
	function selectPackage(packages, libraryName, packageName) {
	    return packages[`${libraryName}::${packageName}`];
	}
	
	return Board;
}

var Sheet = {};

var hasRequiredSheet;

function requireSheet () {
	if (hasRequiredSheet) return Sheet;
	hasRequiredSheet = 1;
	Object.defineProperty(Sheet, "__esModule", { value: true });
	Sheet.Junction = Sheet.Pin = Sheet.Frame = Sheet.Instance = Sheet.SheetLayer = Sheet.Sheet = void 0;
	const Common_1 = requireCommon$1();
	const Label_1 = requireLabel();
	const Wire_1 = requireWire();
	const Rectangle_1 = requireRectangle();
	const Polygon_1 = requirePolygon();
	const Area_1 = requireArea();
	const Point_1 = requirePoint();
	const Rotation_1 = requireRotation();
	class Sheet$1 {
	    static createSheets(sch) {
	        return (0, Common_1.toArray)(sch.drawing.schematic.sheets.sheet).map((sheet) => new Sheet$1(sheet, sch));
	    }
	    constructor(sheet, sch) {
	        this.metadata = Common_1.Metadata.createFromSchematic(sch);
	        this.grid = new Common_1.Grid(sch.drawing.grid);
	        this.layers = {};
	        const boundary = new Area_1.default();
	        sch.drawing.layers.layer.forEach((l) => (this.layers[l._attributes.number] = new SheetLayer(l)));
	        let label;
	        (0, Common_1.toArray)(sheet.plain.text).forEach((t) => {
	            label = Label_1.Label.fromEagleText(t);
	            this.layers[label.layer].labels.push(label);
	            boundary.extendWithArea(label.getArea());
	        });
	        (0, Common_1.toArray)(sheet.nets.net).forEach((n) => {
	            (0, Common_1.toArray)(n.segment).forEach((s) => {
	                let junction;
	                (0, Common_1.toArray)(s.junction).forEach((j) => {
	                    junction = Junction.createFromEagle(j);
	                    this.layers[junction.layer].junctions.push(junction);
	                    boundary.extendWithVertices([junction.point.toXYPosition()]);
	                });
	                let wire;
	                (0, Common_1.toArray)(s.wire).forEach((w) => {
	                    wire = Wire_1.Wire.createFromEagle(w);
	                    this.layers[wire.layer].wires.push(wire);
	                    boundary.extendWithVertices(wire.vertices.map((v) => v.toXYPosition()));
	                });
	            });
	        });
	        let eaglePart;
	        let eagleSymbol;
	        let eagleConnects;
	        (0, Common_1.toArray)(sheet.instances.instance).forEach((i) => {
	            eaglePart = selectPart(i, (0, Common_1.toArray)(sch.drawing.schematic.parts.part));
	            eagleSymbol = selectSymbol(i, (0, Common_1.toArray)(sch.drawing.schematic.parts.part), (0, Common_1.toArray)(sch.drawing.schematic.libraries.library));
	            eagleConnects = selectConnects(i, (0, Common_1.toArray)(sch.drawing.schematic.parts.part), (0, Common_1.toArray)(sch.drawing.schematic.libraries.library));
	            if (!eaglePart || !eagleSymbol) {
	                return;
	            }
	            const instance = new Instance(i, eaglePart);
	            if (instance.nameLabel) {
	                this.layers[instance.nameLabel.layer].labels.push(instance.nameLabel);
	            }
	            if (instance.valueLabel) {
	                this.layers[instance.valueLabel.layer].labels.push(instance.valueLabel);
	            }
	            let circle;
	            (0, Common_1.toArray)(eagleSymbol.circle).forEach((c) => {
	                circle = Common_1.Circle.createFromEagleSchematic(c, instance);
	                this.layers[circle.layer].circles.push(circle);
	                boundary.extendWithArea(circle.getArea());
	            });
	            let wire;
	            (0, Common_1.toArray)(eagleSymbol.wire).forEach((w) => {
	                wire = Wire_1.Wire.createFromEagle(w, instance);
	                this.layers[wire.layer].wires.push(wire);
	                boundary.extendWithVertices(wire.vertices.map((v) => v.toXYPosition()));
	            });
	            let pin;
	            (0, Common_1.toArray)(eagleSymbol.pin).forEach((p) => {
	                pin = new Pin(p, eagleConnects, instance);
	                this.layers[pin.layer].pins.push(pin);
	                boundary.extendWithVertices([pin.p1.toXYPosition(), pin.p2.toXYPosition()]);
	                if (pin.padLabel) {
	                    this.layers[pin.padLabel.layer].labels.push(pin.padLabel);
	                    boundary.extendWithArea(pin.padLabel.getArea());
	                }
	                if (pin.nameLabel) {
	                    this.layers[pin.nameLabel.layer].labels.push(pin.nameLabel);
	                    boundary.extendWithArea(pin.nameLabel.getArea());
	                }
	            });
	            let label;
	            (0, Common_1.toArray)(eagleSymbol.text).forEach((t) => {
	                label = Label_1.Label.fromEagleText(t, instance);
	                if (label.value.startsWith('>') ?? true) {
	                    return;
	                }
	                this.layers[label.layer].labels.push(label);
	                boundary.extendWithArea(label.getArea());
	            });
	            let polygon;
	            (0, Common_1.toArray)(eagleSymbol.polygon).forEach((p) => {
	                polygon = new Polygon_1.Polygon(p, instance);
	                this.layers[polygon.layer].polygons.push(polygon);
	                boundary.extendWithVertices(polygon.vertices.map((v) => v.toXYPosition()));
	            });
	            let rect;
	            (0, Common_1.toArray)(eagleSymbol.rectangle).forEach((r) => {
	                rect = Rectangle_1.Rectangle.createFromEagle(r, instance);
	                this.layers[rect.layer].rectangles.push(rect);
	                boundary.extendWithDimensions(rect.point.x, rect.point.y, rect.width, rect.height);
	            });
	            let frame;
	            (0, Common_1.toArray)(eagleSymbol.frame).forEach((f) => {
	                frame = new Frame(f);
	                this.layers[frame.layer].frames.push(frame);
	                boundary.extendWithVertices([frame.p1.toXYPosition(), frame.p2.toXYPosition()]);
	            });
	        });
	        this.metadata.boundary = boundary;
	    }
	}
	Sheet.Sheet = Sheet$1;
	class SheetLayer {
	    constructor(layer) {
	        this.labels = [];
	        this.wires = [];
	        this.rectangles = [];
	        this.junctions = [];
	        this.circles = [];
	        this.pins = [];
	        this.polygons = [];
	        this.frames = [];
	        this.name = layer._attributes.name;
	        this.visible = 'yes' === layer._attributes.visible;
	        this.color = parseInt(layer._attributes.color);
	        this.number = parseInt(layer._attributes.number);
	        this.fill = parseInt(layer._attributes.fill);
	    }
	}
	Sheet.SheetLayer = SheetLayer;
	class Instance {
	    constructor(i, p) {
	        this.point = new Point_1.Point(i._attributes.x, i._attributes.y);
	        this.name = i._attributes.part + '::' + i._attributes.gate;
	        let rot = i._attributes.rot;
	        if (rot?.startsWith('M')) {
	            this.mirrored = true;
	            rot = rot.substr(1);
	        }
	        this.rotation = (0, Rotation_1.parseRotationFromDegrees)(rot);
	        let { anchor, drawDownXAxis } = Label_1.Label.getDrawDirection(this.rotation);
	        if (this.mirrored) {
	            anchor = (() => {
	                switch (anchor) {
	                    case Label_1.Anchor.TopLeft:
	                        return Label_1.Anchor.TopRight;
	                    case Label_1.Anchor.TopRight:
	                        return Label_1.Anchor.TopLeft;
	                    case Label_1.Anchor.BottomRight:
	                        return Label_1.Anchor.BottomLeft;
	                    case Label_1.Anchor.BottomLeft:
	                        return Label_1.Anchor.BottomRight;
	                    default:
	                        return Label_1.Anchor.BottomRight;
	                }
	            })();
	        }
	        let a = (0, Common_1.toArray)(i.attribute).find((a) => a._attributes.name === 'NAME');
	        if (a && p._attributes.name) {
	            this.nameLabel = new Label_1.Label(new Point_1.Point(a._attributes.x, a._attributes.y), 1.778, 95, p._attributes.name, drawDownXAxis, anchor, false);
	        }
	        a = (0, Common_1.toArray)(i.attribute).find((a) => a._attributes.name === 'VALUE');
	        if (a && p._attributes.value) {
	            this.valueLabel = new Label_1.Label(new Point_1.Point(a._attributes.x, a._attributes.y), 1.778, 96, p._attributes.value, drawDownXAxis, anchor, false);
	        }
	    }
	}
	Sheet.Instance = Instance;
	class Frame {
	    constructor(frame) {
	        this.p1 = new Point_1.Point(frame._attributes.x1, frame._attributes.y1);
	        this.p2 = new Point_1.Point(frame._attributes.x2, frame._attributes.y2);
	        this.rows = parseInt(frame._attributes.rows);
	        this.columns = parseInt(frame._attributes.columns);
	        this.layer = parseInt(frame._attributes.layer);
	    }
	}
	Sheet.Frame = Frame;
	class Pin {
	    constructor(pin, connects, parent) {
	        const connect = connects.find((c) => c._attributes.pin === pin._attributes.name);
	        const dotRadius = 1;
	        const labelDistance = 3;
	        this.layer = 94;
	        const hasDot = !!pin._attributes.function;
	        const showNameLabel = !connect || connect._attributes.pad !== connect._attributes.pin;
	        const pinLength = (() => {
	            switch (pin._attributes.length) {
	                case 'point':
	                    return 0;
	                case 'long':
	                    return 7.5;
	                case 'middle':
	                    return 5;
	                case 'short':
	                default:
	                    return 2.5;
	            }
	        })();
	        this.p1 = new Point_1.Point(pin._attributes.x, pin._attributes.y);
	        this.p1 = this.p1.rotate(parent.rotation);
	        this.p2 = new Point_1.Point(pinLength, 0);
	        let pDot = new Point_1.Point(pinLength - dotRadius, 0);
	        let pNameLabel = new Point_1.Point(pinLength + labelDistance, 0);
	        if (hasDot) {
	            this.p2.x -= dotRadius;
	        }
	        const rotation = (0, Rotation_1.combineRotations)((0, Rotation_1.parseRotationFromDegrees)(pin._attributes.rot), parent.rotation);
	        this.p2 = this.p2.rotate(rotation);
	        pDot = pDot.rotate(rotation);
	        pNameLabel = pNameLabel.rotate(rotation);
	        this.p2.add(this.p1);
	        pDot.add(this.p1);
	        pNameLabel.add(this.p1);
	        if (parent.mirrored) {
	            this.p1.x = -this.p1.x;
	            this.p2.x = -this.p2.x;
	            pDot.x = -pDot.x;
	            pNameLabel.x = -pNameLabel.x;
	        }
	        this.p1.add(parent.point);
	        this.p2.add(parent.point);
	        pDot.add(parent.point);
	        pNameLabel.add(parent.point);
	        if (hasDot) {
	            this.circle = new Common_1.Circle();
	            this.circle.point = pDot;
	            this.circle.layer = this.layer;
	            this.circle.radius = dotRadius;
	        }
	        let anchor;
	        let drawDownXAxis;
	        if ([Rotation_1.Rotation.R0, Rotation_1.Rotation.R180].includes(rotation)) {
	            anchor = this.p2.x > this.p1.x ? Label_1.Anchor.BottomRight : Label_1.Anchor.BottomLeft;
	            drawDownXAxis = true;
	        }
	        else {
	            anchor = this.p2.y > this.p1.y ? Label_1.Anchor.BottomRight : Label_1.Anchor.BottomLeft;
	            drawDownXAxis = false;
	        }
	        const drawInside = !hasDot || ['long', 'middle'].includes(pin._attributes.length);
	        if (drawInside) {
	            anchor = anchor === Label_1.Anchor.BottomLeft ? Label_1.Anchor.BottomRight : Label_1.Anchor.BottomLeft;
	        }
	        if (connect) {
	            this.padLabel = new Label_1.Label(this.p1, 1.778, 95, connect._attributes.pad, drawDownXAxis, anchor, false);
	        }
	        if (showNameLabel) {
	            const { anchor, drawDownXAxis } = Label_1.Label.getDrawDirection(rotation);
	            this.nameLabel = new Label_1.Label(pNameLabel, 1.778, 95, pin._attributes.name, drawDownXAxis, [Label_1.Anchor.TopRight, Label_1.Anchor.BottomRight].includes(anchor) ? Label_1.Anchor.MiddleRight : Label_1.Anchor.MiddleLeft, false);
	        }
	    }
	}
	Sheet.Pin = Pin;
	class Junction {
	    static createFromEagle(j) {
	        const junction = new Junction();
	        junction.point = new Point_1.Point(j._attributes.x, j._attributes.y);
	        junction.layer = 91;
	        return junction;
	    }
	}
	Sheet.Junction = Junction;
	function selectPart(instance, parts) {
	    return parts.find((p) => p._attributes.name === instance._attributes.part);
	}
	function selectSymbol(instance, parts, libraries) {
	    const part = parts.find((p) => p._attributes.name === instance._attributes.part);
	    const library = libraries.find((l) => l._attributes.name === part?._attributes.library);
	    const deviceset = (0, Common_1.toArray)(library?.devicesets.deviceset).find((d) => d._attributes.name === part?._attributes.deviceset);
	    return (0, Common_1.toArray)(library?.symbols.symbol).find((s) => s._attributes.name === (0, Common_1.toArray)(deviceset?.gates.gate)?.[0]._attributes.symbol);
	}
	function selectConnects(instance, parts, libraries) {
	    const part = parts.find((p) => p._attributes.name === instance._attributes.part);
	    const library = libraries.find((l) => l._attributes.name === part?._attributes.library);
	    const deviceset = (0, Common_1.toArray)(library?.devicesets.deviceset).find((d) => d._attributes.name === part?._attributes.deviceset);
	    const device = (0, Common_1.toArray)(deviceset?.devices.device).find((d) => d._attributes.name === part?._attributes.device);
	    return (0, Common_1.toArray)(device?.connects?.connect);
	}
	
	return Sheet;
}

var hasRequiredCommon;

function requireCommon () {
	if (hasRequiredCommon) return Common$1;
	hasRequiredCommon = 1;
	(function (exports) {
		var __createBinding = (Common$1 && Common$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Common$1 && Common$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBoard$1(), exports);
		__exportStar(requireCommon$1(), exports);
		__exportStar(requireLabel(), exports);
		__exportStar(requireRectangle(), exports);
		__exportStar(requireSheet(), exports);
		__exportStar(requireWire(), exports);
		
	} (Common$1));
	return Common$1;
}

var hasRequiredBoard;

function requireBoard () {
	if (hasRequiredBoard) return board;
	hasRequiredBoard = 1;
	Object.defineProperty(board, "__esModule", { value: true });
	board.parseBoard = parseBoard;
	board.parseLibraryPackages = parseLibraryPackages;
	board.readModule = readModule;
	board.readFootprint = readFootprint;
	board.readBoardLayers = readBoardLayers;
	board.readBoardLayer = readBoardLayer;
	board.readTrackFromWire = readTrackFromWire;
	board.readSegmentFromWire = readSegmentFromWire;
	board.readVia = readVia;
	board.readCircle = readCircle;
	board.readRectangle = readRectangle;
	board.readZone = readZone;
	board.readPolygon = readPolygon;
	board.readHole = readHole;
	board.readText = readText;
	board.readElementAttributeToText = readElementAttributeToText;
	board.readPad = readPad;
	board.readSmd = readSmd;
	board.readLayerSetup = readLayerSetup;
	const xml_js_1 = requireLib$1z();
	const layers_1 = requireLayers$1();
	const Area_1 = requireArea();
	const common_1 = requireCommon$2();
	const Util_1 = requireUtil();
	const Transform_1 = requireTransform();
	const fonts_1 = requireFonts();
	const Common_1 = requireCommon();
	const PAD_SHAPE_SQUARE = 'square';
	const PAD_SHAPE_LONG = 'oval';
	const PAD_SHAPE_OCTAGON = 'octagon';
	const VIA_SHAPE_ROUND = 'round';
	const LAYER_DIMENSION = 20;
	const LAYER_COPPER_TOP = 1;
	const LAYER_CREAM_TOP = 31;
	const LAYER_CREAM_BOTTOM = 32;
	const LAYER_SILK_TOP = 21;
	const LAYER_SILK_BOTTOM = 22;
	const LAYER_STOP_TOP = 29;
	const LAYER_STOP_BOTTOM = 30;
	function isCopperLayer(layer) {
	    return !!layer && layer >= 1 && layer <= 16;
	}
	function parseBoard(project, board, designId) {
	    if (project.type !== 'eagle') {
	        throw new Error('Cannot parse eagle board to non-eagle project');
	    }
	    const xmlRoot = parseBoardXml(board);
	    const model = {
	        modelType: 'pcb',
	        designId,
	        sourceFile: designId,
	        edges_bbox: {
	            minx: Number.POSITIVE_INFINITY,
	            miny: Number.POSITIVE_INFINITY,
	            maxx: Number.NEGATIVE_INFINITY,
	            maxy: Number.NEGATIVE_INFINITY,
	        },
	        initial_zoombox: {
	            minx: Number.POSITIVE_INFINITY,
	            miny: Number.POSITIVE_INFINITY,
	            maxx: Number.NEGATIVE_INFINITY,
	            maxy: Number.NEGATIVE_INFINITY,
	        },
	        grid: (0, common_1.readEagleGrid)(xmlRoot.eagle.drawing.grid, 'pcb'),
	        layerSet: layers_1.EAGLE_BRD_LAYERSET,
	        throughHoleLayers: readLayerSetup(xmlRoot.eagle.drawing.board),
	        edges: [],
	        drawings: {},
	        modules: [],
	        tracks: {},
	        vias: [],
	        zones: {},
	        nets: {},
	        font: {
	            default: fonts_1.Font.Newstroke,
	        },
	        metadata: {
	            title: '',
	            revision: '',
	            company: '',
	            date: '',
	        },
	        warnings: [],
	    };
	    function addDrawingToRoot(drawing, layer) {
	        const layerNo = typeof layer === 'number' ? layer : layer.id;
	        if (!model.drawings[layerNo]) {
	            model.drawings[layerNo] = [drawing];
	        }
	        else {
	            model.drawings[layerNo].push(drawing);
	        }
	    }
	    if (Object.keys(project.components).length === 0) {
	        model.warnings.push(`Parse schematics first for proper component linking`);
	    }
	    const packages = collectPackages((0, Common_1.toArray)(xmlRoot.eagle.drawing.board.libraries.library));
	    readBoardLayers(project, xmlRoot, model);
	    const signals = readSignals((0, Common_1.toArray)(xmlRoot.eagle.drawing.board.signals.signal));
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.wire)?.forEach((w) => {
	        const track = readTrackFromWire(w, '');
	        if (track.layer === LAYER_DIMENSION) {
	            const segment = readSegmentFromWire(w);
	            model.edges.push(segment);
	        }
	        else if (isCopperLayer(track.layer)) {
	            model.tracks[track.layer] = model.tracks[track.layer] ?? [];
	            model.tracks[track.layer].push(track);
	        }
	        else {
	            const segment = readSegmentFromWire(w);
	            addDrawingToRoot(segment, track.layer);
	        }
	    });
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.rectangle)?.forEach((r) => {
	        const rectangle = readRectangle(r);
	        addDrawingToRoot(rectangle.shape, rectangle.layer);
	    });
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.circle)?.forEach((c) => {
	        const { shape, layer } = readCircle(c);
	        addDrawingToRoot(shape, layer);
	    });
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.hole)?.forEach((h) => {
	        model.edges.push(readHole(h));
	    });
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.plain.text)?.forEach((t) => {
	        const { shape, layer } = readText(t);
	        addDrawingToRoot(shape, layer);
	    });
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.signals.signal)?.forEach((s) => {
	        (0, Common_1.toArray)(s.wire).forEach((w) => {
	            const track = readTrackFromWire(w, s._attributes.name);
	            if (typeof track.layer === 'number') {
	                model.tracks[track.layer] = model.tracks[track.layer] ?? [];
	                model.tracks[track.layer].push(track);
	            }
	            else {
	                model.warnings.push(`Layer ${track.layer} not implemented.`);
	            }
	        });
	        (0, Common_1.toArray)(s.via).forEach((v) => {
	            const via = readVia(v, s._attributes.name, model.throughHoleLayers);
	            model.vias.push(via);
	        });
	        (0, Common_1.toArray)(s.polygon).forEach((p) => {
	            const zone = readZone(p, s._attributes.name);
	            if (typeof zone.layer === 'number') {
	                model.zones[zone.layer] = model.zones[zone.layer] ?? [];
	                model.zones[zone.layer].push(zone);
	            }
	            else {
	                model.warnings.push(`Layer ${zone.layer} not implemented.`);
	            }
	        });
	    });
	    (0, Common_1.toArray)(xmlRoot.eagle.drawing.board.elements.element).forEach((e) => {
	        let eaglePackage = selectPackage(packages, e._attributes.library, e._attributes.package);
	        const { footprint, module, value } = readModule(project, e, eaglePackage, signals);
	        const component = project.components[module.componentRef];
	        if (!component) {
	            project.components[footprint.ref] = {
	                name: footprint.ref,
	                footprints: [footprint],
	                signals: [],
	                symbols: [],
	                numPins: footprint.pads.length,
	                numPads: footprint.pads.length,
	            };
	        }
	        model.modules.push(module);
	        for (const [pin, net] of Object.entries(module.pinNets)) {
	            if (!model.nets[net]) {
	                model.nets[net] = {
	                    name: net,
	                    connections: [],
	                    isGlobal: false,
	                };
	                if (net.toLowerCase().endsWith('_p') || net.toLowerCase().endsWith('_n')) {
	                    const pairName = net.slice(0, -2);
	                    model.nets[net].diffPair = {
	                        pairName,
	                        positiveId: net.toLowerCase().endsWith('_p') ? net : pairName + '_P',
	                        negativeId: net.toLowerCase().endsWith('_n') ? net : pairName + '_N',
	                    };
	                }
	            }
	            model.nets[net].connections.push({
	                moduleRef: module.ref,
	                pinRef: pin,
	                moduleName: module.name,
	            });
	        }
	        let metaModule = project.modules.find((m) => m.name === module.name);
	        if (!metaModule) {
	            metaModule = {
	                name: module.fullModuleName ?? module.name,
	                instances: [],
	                componentRef: module.componentRef,
	            };
	            project.modules.push(metaModule);
	        }
	        if (!metaModule.name) {
	            metaModule.name = module.name;
	        }
	        if (value) {
	            if (metaModule.value && metaModule.value !== value) {
	                model.warnings.push(`Module ${module.name} has multiple values: ${metaModule.value} and ${value}`);
	            }
	            metaModule.value = value;
	        }
	        if (module.metadata && Object.keys(module.metadata).length > 0) {
	            if (!metaModule.metadata) {
	                metaModule.metadata = {};
	            }
	            metaModule.metadata = {
	                ...metaModule.metadata,
	                ...module.metadata,
	            };
	        }
	        metaModule.instances.push({
	            ref: module.ref,
	            designId,
	        });
	    });
	    for (const net of Object.values(model.nets)) {
	        if (!net.diffPair) {
	            continue;
	        }
	        if (!model.nets[net.diffPair.positiveId]) {
	            model.warnings.push(`Could not find positive net for differential pair '${net.diffPair.positiveId}'`);
	        }
	        if (!model.nets[net.diffPair.positiveId]) {
	            model.warnings.push(`Could not find negative net for differential pair '${net.diffPair.positiveId}'`);
	        }
	    }
	    const bbox = new Area_1.default();
	    bbox.extendFromPcbModel(project, model, project.layerSets[layers_1.EAGLE_BRD_LAYERSET], model.warnings);
	    model.edges_bbox = {
	        minx: bbox.xMin,
	        miny: bbox.yMin,
	        maxx: bbox.xMax,
	        maxy: bbox.yMax,
	    };
	    const visibleBbox = new Area_1.default();
	    visibleBbox.extendFromPcbModel(project, model, project.layerSets[layers_1.EAGLE_BRD_LAYERSET], model.warnings, false);
	    model.initial_zoombox = {
	        minx: visibleBbox.xMin,
	        miny: visibleBbox.yMin,
	        maxx: visibleBbox.xMax,
	        maxy: visibleBbox.yMax,
	    };
	    (0, Util_1.dedupeWarnings)(model);
	    return model;
	}
	function parseLibraryPackages(library, filename) {
	    const xmlRoot = parseLibraryXml(library);
	    return collectPackages((0, Common_1.toArray)(xmlRoot.eagle.drawing.library), filename);
	}
	function parseBoardXml(board) {
	    const json = (0, xml_js_1.xml2json)(board, { compact: true });
	    return JSON.parse(json);
	}
	function parseLibraryXml(library) {
	    const json = (0, xml_js_1.xml2json)(library, { compact: true });
	    return JSON.parse(json);
	}
	function collectPackages(libs, defaultLibName = '<unknownlib>') {
	    const packages = {};
	    libs.forEach((l) => {
	        (0, Common_1.toArray)(l.packages.package).forEach((p) => {
	            packages[`${l._attributes?.name || defaultLibName}::${p._attributes.name}`] = p;
	        });
	    });
	    return packages;
	}
	function selectPackage(packages, libraryName, packageName) {
	    return packages[`${libraryName}::${packageName}`];
	}
	function readModule(project, element, eaglePackage, signals) {
	    const rotation = (0, common_1.parseRotation)(element._attributes.rot);
	    const module = {
	        ref: element._attributes.name,
	        footprintRef: '',
	        componentRef: '',
	        name: element._attributes.name,
	        center: [parseFloat(element._attributes.x), -parseFloat(element._attributes.y)],
	        transform: {
	            pos: [0, 0],
	            angle: -1 * rotation.rot,
	            mirrored: rotation.mirrored,
	        },
	        pinNets: {},
	        drawProperties: {},
	        layer: element._attributes.rot?.startsWith('MR') ? 'B' : 'F',
	    };
	    const footprint = readFootprint(eaglePackage, element._attributes.name, element._attributes.library, module, signals);
	    module.footprintRef = footprint.ref;
	    module.componentRef = getComponentRefForElement(project, element._attributes.name, footprint.ref);
	    const value = element._attributes.value?.trim();
	    (0, Common_1.toArray)(element.attribute).forEach((r) => {
	        readElementAttributeToText(r, module, value);
	    });
	    return { footprint, module, value };
	}
	function readFootprint(eaglePackage, deviceName, libraryName, module, signals = []) {
	    const footprint = {
	        ref: `${libraryName}::footprint::${eaglePackage._attributes.name}`,
	        pads: [],
	        drawings: [],
	        name: `${libraryName}::footprint::${eaglePackage._attributes.name}`,
	        metadata: {
	            'eagle.library': libraryName,
	        },
	    };
	    (0, common_1.addMetadata)(footprint, 'eagle.package.name', eaglePackage._attributes?.name);
	    (0, Common_1.toArray)(eaglePackage.pad).forEach((p) => {
	        const { pad, net } = readPad(p, deviceName, signals);
	        if (module) {
	            module.pinNets[pad.ref] = net;
	        }
	        footprint.pads.push(pad);
	    });
	    (0, Common_1.toArray)(eaglePackage.smd).forEach((smd) => {
	        const { pad, net } = readSmd(smd, deviceName, signals);
	        if (module) {
	            module.pinNets[pad.ref] = net;
	        }
	        footprint.pads.push(pad);
	    });
	    (0, Common_1.toArray)(eaglePackage.wire).forEach((w) => {
	        const track = readTrackFromWire(w, '');
	        if (track.layer === LAYER_SILK_BOTTOM || track.layer === LAYER_SILK_TOP) {
	            const segment = readSegmentFromWire(w);
	            footprint.drawings.push({ layers: [track.layer], drawing: segment });
	        }
	        else {
	            footprint.drawings.push({ layers: [track.layer], drawing: track });
	        }
	    });
	    (0, Common_1.toArray)(eaglePackage.polygon).forEach((p) => {
	        const poly = readPolygon(p);
	        const polyLayer = parseInt(p._attributes.layer);
	        if (typeof polyLayer === 'number') {
	            footprint.drawings.push({ layers: [polyLayer], drawing: poly });
	        }
	    });
	    (0, Common_1.toArray)(eaglePackage.rectangle).forEach((r) => {
	        const { shape, layer } = readRectangle(r);
	        footprint.drawings.push({ layers: [layer], drawing: shape });
	    });
	    (0, Common_1.toArray)(eaglePackage.circle).forEach((c) => {
	        const { shape, layer } = readCircle(c);
	        footprint.drawings.push({ layers: [layer], drawing: shape });
	    });
	    (0, Common_1.toArray)(eaglePackage.hole).forEach((c) => {
	        const hole = readHole(c);
	        footprint.drawings.push({ layers: [layers_1.LAYER_EDGE], drawing: hole });
	    });
	    if (eaglePackage.description) {
	        footprint.metadata['description'] = (0, Common_1.toArray)(eaglePackage.description)
	            .map((d) => d._text)
	            .join('\n');
	    }
	    (0, Common_1.toArray)(eaglePackage.text).forEach((r) => {
	        const { shape, layer } = readText(r);
	        footprint.drawings.push({ layers: [layer], drawing: shape });
	    });
	    return footprint;
	}
	function readBoardLayers(project, xmlRoot, model) {
	    if (project.layerSets[layers_1.EAGLE_BRD_LAYERSET]) {
	        return;
	    }
	    const layers = [];
	    xmlRoot.eagle.drawing.layers.layer.forEach((l) => {
	        if (!(0, layers_1.isLayerActive)(l)) {
	            return;
	        }
	        layers.push(readBoardLayer(l));
	    });
	    for (const layer of layers) {
	        if (!layer.name) {
	            model.warnings.push(`Layer ${layer.id} did not have a name associated`);
	            continue;
	        }
	        const match = /^(_?)(t|b)([A-Z]\S+)$/.exec(layer.name);
	        if (typeof layer.face === 'undefined' && match) {
	            if (match[2] === 't') {
	                layer.face = 'F';
	            }
	            else if (match[2] === 'b') {
	                layer.face = 'B';
	            }
	        }
	        if (typeof layer.face !== 'undefined' && typeof layer.faceMirrorLayer === 'undefined' && match) {
	            const oppositeLayerName = [match[1] || '', layer.face === 'F' ? 'b' : 't', match[3]].join('');
	            const opposite = layers.find((l) => l.name === oppositeLayerName);
	            if (opposite) {
	                if (typeof opposite.faceMirrorLayer !== 'undefined') {
	                    if (opposite.faceMirrorLayer !== layer.id) {
	                        model.warnings.push(`Layer ${layer.id} should have opposite ${opposite.id}, but opposite has faceMirrorLayer ${opposite.faceMirrorLayer}`);
	                        continue;
	                    }
	                }
	                layer.faceMirrorLayer = opposite.id;
	                opposite.faceMirrorLayer = layer.id;
	            }
	        }
	    }
	    project.layerSets[layers_1.EAGLE_BRD_LAYERSET] = layers;
	}
	function readBoardLayer(layer) {
	    const layerId = parseInt(layer._attributes.number);
	    const parsed = (0, layers_1.parsePcbLayer)(layerId, layer._attributes.name);
	    if ((0, layers_1.isLayerVisible)(layer)) {
	        parsed.defaultVisible = true;
	    }
	    return parsed;
	}
	function readTrackFromWire(wire, net) {
	    return {
	        type: 'track',
	        start: [parseFloat(wire._attributes.x1), -parseFloat(wire._attributes.y1)],
	        end: [parseFloat(wire._attributes.x2), -parseFloat(wire._attributes.y2)],
	        width: parseFloat(wire._attributes.width || '0'),
	        net,
	        layer: parseInt(wire._attributes.layer),
	    };
	}
	function readSegmentFromWire(wire) {
	    const width = parseFloat(wire._attributes.width ?? '0');
	    return {
	        type: 'segment',
	        start: [parseFloat(wire._attributes.x1), -parseFloat(wire._attributes.y1)],
	        end: [parseFloat(wire._attributes.x2), -parseFloat(wire._attributes.y2)],
	        width: width >= 0 ? width : 0,
	        curve: parseFloat(wire._attributes.curve ?? '0'),
	    };
	}
	function readVia(via, net, viaLayers) {
	    const diameter = parseFloat(via._attributes.drill) * 2;
	    let polygons = [];
	    let shape;
	    switch (via._attributes.shape) {
	        case PAD_SHAPE_SQUARE:
	            shape = 'rect';
	            break;
	        case PAD_SHAPE_OCTAGON: {
	            shape = 'custom';
	            polygons = getOctagonPoints(diameter / 2);
	            break;
	        }
	        case VIA_SHAPE_ROUND:
	        default:
	            shape = 'circle';
	    }
	    return {
	        type: 'via',
	        center: [parseFloat(via._attributes.x), -parseFloat(via._attributes.y)],
	        diameter: parseFloat(via._attributes.drill) * 2,
	        drillDiameter: parseFloat(via._attributes.drill),
	        layers: [...viaLayers, 18],
	        net,
	        shape,
	        polygons: polygons.length ? polygons : undefined,
	    };
	}
	function readCircle(c) {
	    return {
	        layer: parseFloat(c._attributes.layer),
	        shape: {
	            type: 'circle',
	            center: [parseFloat(c._attributes.x), -parseFloat(c._attributes.y)],
	            radius: parseFloat(c._attributes.radius),
	            filled: false,
	        },
	    };
	}
	function readRectangle(r, net) {
	    return {
	        layer: parseFloat(r._attributes.layer),
	        shape: {
	            type: 'rect',
	            start: [parseFloat(r._attributes.x1), -parseFloat(r._attributes.y1)],
	            end: [parseFloat(r._attributes.x2), -parseFloat(r._attributes.y2)],
	            filled: true,
	            strokeWidth: r._attributes.width ? parseFloat(r._attributes.width) : 0,
	        },
	    };
	}
	function readZone(polygon, net) {
	    const points = polygon.vertex.map((v) => [parseFloat(v._attributes.x), -parseFloat(v._attributes.y)]);
	    points.push(points[0]);
	    return {
	        type: 'zone',
	        layer: parseFloat(polygon._attributes.layer),
	        polygons: [
	            {
	                points,
	                filled: false,
	            },
	        ],
	        net,
	    };
	}
	function readPolygon(polygon, metadata) {
	    const points = polygon.vertex.map((v) => [parseFloat(v._attributes.x), -parseFloat(v._attributes.y)]);
	    points.push(points[0]);
	    return {
	        type: 'polygon',
	        points,
	        width: parseFloat(polygon._attributes.width),
	        filled: true,
	        metadata,
	    };
	}
	function readHole(hole) {
	    return {
	        type: 'circle',
	        center: [parseFloat(hole._attributes.x), -parseFloat(hole._attributes.y)],
	        radius: parseFloat(hole._attributes.drill) / 2,
	        filled: true,
	    };
	}
	function readText(text, isPositionOffset, rejustify) {
	    let ref_designator = false;
	    let ref = undefined;
	    if (text._text?.startsWith('>')) {
	        ref_designator = true;
	        ref = text._text.substring(1);
	    }
	    let rot = (0, common_1.parseRotation)(text._attributes.rot);
	    let justify = [-1, -1];
	    rot.rot *= -1;
	    if (rejustify) {
	        let result = (0, Transform_1.rejustifyTextUpwards)(justify, rot);
	        rot = result.rot;
	        justify = result.justify;
	    }
	    return {
	        layer: parseFloat(text._attributes.layer),
	        shape: {
	            type: 'text',
	            size: parseFloat(text._attributes.size),
	            thickness: 1,
	            pos: [parseFloat(text._attributes.x), -parseFloat(text._attributes.y)],
	            justify,
	            angle: rot.rot,
	            mirror: rot.mirrored,
	            attr: [],
	            ref,
	            ref_designator,
	            val: false,
	            value: text._text,
	            isPositionOffset,
	            rejustifyOnRotate: rejustify,
	        },
	    };
	}
	function readElementAttributeToText(text, module, value) {
	    const rotation = (0, common_1.parseRotation)(text._attributes.rot);
	    rotation.rot *= -1;
	    const { justify, rot } = (0, Transform_1.rejustifyTextUpwards)([-1, -1], rotation);
	    const nameLower = text._attributes.name.toLowerCase();
	    if (text._attributes.value) {
	        value = text._attributes.value;
	    }
	    else if (nameLower === 'name') {
	        value = module.name;
	    }
	    module.drawProperties[text._attributes.name] = {
	        angle: rot.rot,
	        mirror: rot.mirrored,
	        justify,
	        pos: [parseFloat(text._attributes.x), -parseFloat(text._attributes.y)],
	        value: value,
	        size: parseFloat(text._attributes.size),
	        thickness: 1,
	        layer: text._attributes.layer ? parseInt(text._attributes.layer) : undefined,
	        hidden: typeof text._attributes.display === 'string' && !(0, common_1.eagleBoolean)(text._attributes.display),
	        rejustifyOnRotate: true,
	    };
	}
	function readPad(pad, footprintName, signals) {
	    const name = pad._attributes.name;
	    let diameter = pad._attributes.diameter ? parseFloat(pad._attributes.diameter) : 0;
	    const drillDiameter = pad._attributes.drill ? parseFloat(pad._attributes.drill) : 0;
	    const signal = signals.find((s) => s.contactRefs.find((c) => c.element === footprintName && c.pad === name));
	    if (diameter <= 0 && drillDiameter > 0) {
	        switch (pad._attributes.shape) {
	            case 'octagon':
	                diameter = 1.52;
	                break;
	            default:
	                diameter = drillDiameter;
	        }
	    }
	    const pos = [parseFloat(pad._attributes.x), -parseFloat(pad._attributes.y)];
	    let polygons = [];
	    let shape;
	    switch (pad._attributes.shape) {
	        case PAD_SHAPE_SQUARE:
	            shape = 'rect';
	            break;
	        case PAD_SHAPE_LONG:
	            shape = 'oval';
	            break;
	        case PAD_SHAPE_OCTAGON: {
	            shape = 'custom';
	            polygons = getOctagonPoints(diameter / 2);
	            break;
	        }
	        default:
	            shape = 'circle';
	    }
	    const result = {
	        type: 'pad',
	        ref: pad._attributes.name,
	        name: pad._attributes.name,
	        pos,
	        polygons: polygons.length ? polygons : undefined,
	        angle: -(0, common_1.parseRotation)(pad._attributes.rot).rot,
	        pin1: name === '1' ? 1 : undefined,
	        shape: shape,
	        padType: 'th',
	        diameter: [diameter, diameter],
	    };
	    if (drillDiameter > 0) {
	        result.drillshape = shape === 'oval' ? 'oblong' : 'circle';
	        result.drillDiameter = [drillDiameter, drillDiameter];
	    }
	    return { pad: result, net: signal?.name ?? '' };
	}
	function readSmd(smd, footprintName, signals) {
	    const name = smd._attributes.name;
	    const signal = signals.find((s) => s.contactRefs.find((c) => c.element === footprintName && c.pad === name));
	    const pos = [parseFloat(smd._attributes.x), -parseFloat(smd._attributes.y)];
	    const shape = 'roundrect';
	    const roundness = smd._attributes.roundness ? parseFloat(smd._attributes.roundness) : 0;
	    const diameter = [parseFloat(smd._attributes.dx), parseFloat(smd._attributes.dy)];
	    const smdLayer = parseFloat(smd._attributes.layer);
	    const result = {
	        type: 'pad',
	        ref: smd._attributes.name,
	        name: smd._attributes.name,
	        layers: [smdLayer],
	        pos,
	        angle: -(0, common_1.parseRotation)(smd._attributes.rot).rot,
	        pin1: name === '1',
	        shape: shape,
	        padType: 'smd',
	        diameter,
	        radius: (roundness / 100) * Math.max(...diameter),
	    };
	    if ((0, common_1.eagleBoolean)(smd._attributes.stop, true)) {
	        result.layers.push(smdLayer === LAYER_COPPER_TOP ? LAYER_STOP_TOP : LAYER_STOP_BOTTOM);
	    }
	    if ((0, common_1.eagleBoolean)(smd._attributes.cream, true)) {
	        result.layers.push(smdLayer === LAYER_COPPER_TOP ? LAYER_CREAM_TOP : LAYER_CREAM_BOTTOM);
	    }
	    return {
	        pad: result,
	        net: signal?.name ?? '',
	    };
	}
	function readLayerSetup(board) {
	    const nonCopperThroughHoleLayers = [LAYER_STOP_TOP, LAYER_STOP_BOTTOM, layers_1.LAYER_EDGE];
	    const descriptionLayerSetup = board.designrules.param.find((p) => p._attributes.name === 'layerSetup');
	    if (descriptionLayerSetup) {
	        const values = descriptionLayerSetup._attributes.value.split(/[^0-9.]/g);
	        return values
	            .filter((v) => v.length)
	            .map((v) => parseInt(v))
	            .concat(nonCopperThroughHoleLayers)
	            .sort((a, b) => a - b);
	    }
	    return [layers_1.LAYER_FRONT, layers_1.LAYER_BACK, ...nonCopperThroughHoleLayers];
	}
	function readSignals(signals) {
	    return signals.map((s) => {
	        return {
	            name: s._attributes.name,
	            class: s._attributes.class,
	            contactRefs: (0, Common_1.toArray)(s.contactref).map((c) => c._attributes),
	        };
	    });
	}
	function getOctagonPoints(radius) {
	    radius = radius * 1.082883029;
	    const startAngle = Math.PI / 8;
	    const calcX = (xi, offset) => xi + radius * Math.cos(startAngle + (offset * Math.PI) / 4);
	    const calcY = (xi, offset) => xi + radius * Math.sin(startAngle + (offset * Math.PI) / 4);
	    return [
	        [
	            [calcX(0, 0), -calcY(0, 0)],
	            [calcX(0, 1), -calcY(0, 1)],
	            [calcX(0, 2), -calcY(0, 2)],
	            [calcX(0, 3), -calcY(0, 3)],
	            [calcX(0, 4), -calcY(0, 4)],
	            [calcX(0, 5), -calcY(0, 5)],
	            [calcX(0, 6), -calcY(0, 6)],
	            [calcX(0, 7), -calcY(0, 7)],
	            [calcX(0, 0), -calcY(0, 0)],
	        ],
	    ];
	}
	function getComponentRefForElement(project, name, defaultRef) {
	    for (const [, design] of Object.entries(project.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        for (const module of design.modules) {
	            if (module.name === name) {
	                return module.componentRef;
	            }
	        }
	    }
	    return defaultRef;
	}
	
	return board;
}

var schematic = {};

var levenshtein = {exports: {}};

const peq = new Uint32Array(0x10000);
const myers_32 = (a, b) => {
    const n = a.length;
    const m = b.length;
    const lst = 1 << (n - 1);
    let pv = -1;
    let mv = 0;
    let sc = n;
    let i = n;
    while (i--) {
        peq[a.charCodeAt(i)] |= 1 << i;
    }
    for (i = 0; i < m; i++) {
        let eq = peq[b.charCodeAt(i)];
        const xv = eq | mv;
        eq |= ((eq & pv) + pv) ^ pv;
        mv |= ~(eq | pv);
        pv &= eq;
        if (mv & lst) {
            sc++;
        }
        if (pv & lst) {
            sc--;
        }
        mv = (mv << 1) | 1;
        pv = (pv << 1) | ~(xv | mv);
        mv &= xv;
    }
    i = n;
    while (i--) {
        peq[a.charCodeAt(i)] = 0;
    }
    return sc;
};
const myers_x = (b, a) => {
    const n = a.length;
    const m = b.length;
    const mhc = [];
    const phc = [];
    const hsize = Math.ceil(n / 32);
    const vsize = Math.ceil(m / 32);
    for (let i = 0; i < hsize; i++) {
        phc[i] = -1;
        mhc[i] = 0;
    }
    let j = 0;
    for (; j < vsize - 1; j++) {
        let mv = 0;
        let pv = -1;
        const start = j * 32;
        const vlen = Math.min(32, m) + start;
        for (let k = start; k < vlen; k++) {
            peq[b.charCodeAt(k)] |= 1 << k;
        }
        for (let i = 0; i < n; i++) {
            const eq = peq[a.charCodeAt(i)];
            const pb = (phc[(i / 32) | 0] >>> i) & 1;
            const mb = (mhc[(i / 32) | 0] >>> i) & 1;
            const xv = eq | mv;
            const xh = ((((eq | mb) & pv) + pv) ^ pv) | eq | mb;
            let ph = mv | ~(xh | pv);
            let mh = pv & xh;
            if ((ph >>> 31) ^ pb) {
                phc[(i / 32) | 0] ^= 1 << i;
            }
            if ((mh >>> 31) ^ mb) {
                mhc[(i / 32) | 0] ^= 1 << i;
            }
            ph = (ph << 1) | pb;
            mh = (mh << 1) | mb;
            pv = mh | ~(xv | ph);
            mv = ph & xv;
        }
        for (let k = start; k < vlen; k++) {
            peq[b.charCodeAt(k)] = 0;
        }
    }
    let mv = 0;
    let pv = -1;
    const start = j * 32;
    const vlen = Math.min(32, m - start) + start;
    for (let k = start; k < vlen; k++) {
        peq[b.charCodeAt(k)] |= 1 << k;
    }
    let score = m;
    for (let i = 0; i < n; i++) {
        const eq = peq[a.charCodeAt(i)];
        const pb = (phc[(i / 32) | 0] >>> i) & 1;
        const mb = (mhc[(i / 32) | 0] >>> i) & 1;
        const xv = eq | mv;
        const xh = ((((eq | mb) & pv) + pv) ^ pv) | eq | mb;
        let ph = mv | ~(xh | pv);
        let mh = pv & xh;
        score += (ph >>> (m - 1)) & 1;
        score -= (mh >>> (m - 1)) & 1;
        if ((ph >>> 31) ^ pb) {
            phc[(i / 32) | 0] ^= 1 << i;
        }
        if ((mh >>> 31) ^ mb) {
            mhc[(i / 32) | 0] ^= 1 << i;
        }
        ph = (ph << 1) | pb;
        mh = (mh << 1) | mb;
        pv = mh | ~(xv | ph);
        mv = ph & xv;
    }
    for (let k = start; k < vlen; k++) {
        peq[b.charCodeAt(k)] = 0;
    }
    return score;
};
const distance = (a, b) => {
    if (a.length < b.length) {
        const tmp = b;
        b = a;
        a = tmp;
    }
    if (b.length === 0) {
        return a.length;
    }
    if (a.length <= 32) {
        return myers_32(a, b);
    }
    return myers_x(a, b);
};
const closest = (str, arr) => {
    let min_distance = Infinity;
    let min_index = 0;
    for (let i = 0; i < arr.length; i++) {
        const dist = distance(str, arr[i]);
        if (dist < min_distance) {
            min_distance = dist;
            min_index = i;
        }
    }
    return arr[min_index];
};

var mod = /*#__PURE__*/Object.freeze({
  __proto__: null,
  closest: closest,
  distance: distance
});

var require$$0 = /*@__PURE__*/getAugmentedNamespace(mod);

levenshtein.exports;

var hasRequiredLevenshtein;

function requireLevenshtein () {
	if (hasRequiredLevenshtein) return levenshtein.exports;
	hasRequiredLevenshtein = 1;
	(function (module, exports) {
		(function() {
		  
		  var collator;
		  try {
		    collator = (typeof Intl !== "undefined" && typeof Intl.Collator !== "undefined") ? Intl.Collator("generic", { sensitivity: "base" }) : null;
		  } catch (err){
		    console.log("Collator could not be initialized and wouldn't be used");
		  }

		  var levenshtein = require$$0;

		  // arrays to re-use
		  var prevRow = [],
		    str2Char = [];
		  
		  /**
		   * Based on the algorithm at http://en.wikipedia.org/wiki/Levenshtein_distance.
		   */
		  var Levenshtein = {
		    /**
		     * Calculate levenshtein distance of the two strings.
		     *
		     * @param str1 String the first string.
		     * @param str2 String the second string.
		     * @param [options] Additional options.
		     * @param [options.useCollator] Use `Intl.Collator` for locale-sensitive string comparison.
		     * @return Integer the levenshtein distance (0 and above).
		     */
		    get: function(str1, str2, options) {
		      var useCollator = (options && collator && options.useCollator);
		      
		      if (useCollator) {
		        var str1Len = str1.length,
		          str2Len = str2.length;
		        
		        // base cases
		        if (str1Len === 0) return str2Len;
		        if (str2Len === 0) return str1Len;

		        // two rows
		        var curCol, nextCol, i, j, tmp;

		        // initialise previous row
		        for (i=0; i<str2Len; ++i) {
		          prevRow[i] = i;
		          str2Char[i] = str2.charCodeAt(i);
		        }
		        prevRow[str2Len] = str2Len;

		        var strCmp;
		        // calculate current row distance from previous row using collator
		        for (i = 0; i < str1Len; ++i) {
		          nextCol = i + 1;

		          for (j = 0; j < str2Len; ++j) {
		            curCol = nextCol;

		            // substution
		            strCmp = 0 === collator.compare(str1.charAt(i), String.fromCharCode(str2Char[j]));

		            nextCol = prevRow[j] + (strCmp ? 0 : 1);

		            // insertion
		            tmp = curCol + 1;
		            if (nextCol > tmp) {
		              nextCol = tmp;
		            }
		            // deletion
		            tmp = prevRow[j + 1] + 1;
		            if (nextCol > tmp) {
		              nextCol = tmp;
		            }

		            // copy current col value into previous (in preparation for next iteration)
		            prevRow[j] = curCol;
		          }

		          // copy last col value into previous (in preparation for next iteration)
		          prevRow[j] = nextCol;
		        }
		        return nextCol;
		      }
		      return levenshtein.distance(str1, str2);
		    }

		  };

		  // amd
		  if (module !== null && 'object' !== "undefined" && module.exports === exports) {
		    module.exports = Levenshtein;
		  }
		  // web worker
		  else if (typeof self !== "undefined" && typeof self.postMessage === 'function' && typeof self.importScripts === 'function') {
		    self.Levenshtein = Levenshtein;
		  }
		  // browser main thread
		  else if (typeof window !== "undefined" && window !== null) {
		    window.Levenshtein = Levenshtein;
		  }
		}()); 
	} (levenshtein, levenshtein.exports));
	return levenshtein.exports;
}

var ObjMap = {};

var hasRequiredObjMap;

function requireObjMap () {
	if (hasRequiredObjMap) return ObjMap;
	hasRequiredObjMap = 1;
	Object.defineProperty(ObjMap, "__esModule", { value: true });
	ObjMap.toMap = toMap;
	function toMap(objects, keyFunc, transform = (o) => o) {
	    const m = {};
	    objects.forEach((o) => (m[keyFunc(o)] = transform(o)));
	    return m;
	}
	
	return ObjMap;
}

var hasRequiredSchematic;

function requireSchematic () {
	if (hasRequiredSchematic) return schematic;
	hasRequiredSchematic = 1;
	Object.defineProperty(schematic, "__esModule", { value: true });
	schematic.parseSchematic = parseSchematic;
	schematic.parseSheet = parseSheet;
	schematic.getComponentName = getComponentName;
	schematic.parseComponentSignals = parseComponentSignals;
	schematic.parseComponentDefinition = parseComponentDefinition;
	schematic.parseSchematicXml = parseSchematicXml;
	schematic.getSymbol = getSymbol;
	schematic.loadSymbol = loadSymbol;
	const xml_js_1 = requireLib$1z();
	const common_1 = requireCommon$2();
	const Area_1 = requireArea();
	const layers_1 = requireLayers$1();
	const board_1 = requireBoard();
	const Util_1 = requireUtil();
	const strDist = requireLevenshtein();
	const sigma_engine_shared_1 = requireDist$1();
	const Common_1 = requireCommon();
	const Transform_1 = requireTransform();
	const fonts_1 = requireFonts();
	const schematicUtils_1 = requireSchematicUtils();
	const ObjMap_1 = requireObjMap();
	const PROP_NAME = 'NAME';
	const PROP_VALUE = 'VALUE';
	var PinDirection;
	(function (PinDirection) {
	    PinDirection["NotConnected"] = "nc";
	    PinDirection["Input"] = "in";
	    PinDirection["Output"] = "out";
	    PinDirection["BidirectionalIO"] = "i/o";
	    PinDirection["OpenCollectorOpenDrain"] = "oc";
	    PinDirection["HiImpedence"] = "hiz";
	    PinDirection["Passive"] = "pas";
	    PinDirection["Power"] = "pwr";
	    PinDirection["Supply"] = "sup";
	})(PinDirection || (PinDirection = {}));
	var PinVisibility;
	(function (PinVisibility) {
	    PinVisibility["Off"] = "off";
	    PinVisibility["Pad"] = "pad";
	    PinVisibility["Pin"] = "pin";
	    PinVisibility["Both"] = "both";
	})(PinVisibility || (PinVisibility = {}));
	var PinLength;
	(function (PinLength) {
	    PinLength["Point"] = "point";
	    PinLength["Short"] = "short";
	    PinLength["Middle"] = "middle";
	    PinLength["Long"] = "long";
	})(PinLength || (PinLength = {}));
	var SignalParametrics;
	(function (SignalParametrics) {
	    SignalParametrics["HiImpedence"] = "hiimpedence";
	})(SignalParametrics || (SignalParametrics = {}));
	function parseSchematic(project, schematic, designId) {
	    if (project.type !== 'eagle') {
	        throw new Error('Cannot parse eagle schematic to non-eagle project');
	    }
	    const xmlRoot = parseSchematicXml(schematic);
	    const schematicXml = xmlRoot.eagle.drawing.schematic;
	    if (!project.layerSets[layers_1.EAGLE_SCH_LAYERSET]) {
	        project.layerSets[layers_1.EAGLE_SCH_LAYERSET] = readLayers(xmlRoot);
	    }
	    const grid = (0, common_1.readEagleGrid)(xmlRoot.eagle.drawing.grid, 'schematic');
	    return (0, Common_1.toArray)(schematicXml.sheets.sheet).map((sheet, index) => parseSheet(project, schematicXml, grid, designId, sheet, index));
	}
	function parseSheet(project, schematicXml, grid, sourceFile, sheet, sheetIndex) {
	    const model = {
	        modelType: 'schematic',
	        designId: `${sourceFile} sheet ${sheetIndex}`,
	        sourceFile,
	        sheetIndex,
	        edges_bbox: {
	            minx: Number.POSITIVE_INFINITY,
	            miny: Number.POSITIVE_INFINITY,
	            maxx: Number.NEGATIVE_INFINITY,
	            maxy: Number.NEGATIVE_INFINITY,
	        },
	        initial_zoombox: {
	            minx: Number.POSITIVE_INFINITY,
	            miny: Number.POSITIVE_INFINITY,
	            maxx: Number.NEGATIVE_INFINITY,
	            maxy: Number.NEGATIVE_INFINITY,
	        },
	        grid,
	        layerSet: layers_1.EAGLE_SCH_LAYERSET,
	        drawings: {},
	        modules: [],
	        nets: {},
	        font: {
	            default: fonts_1.Font.Newstroke,
	        },
	        warnings: [],
	        ports: {},
	    };
	    (0, Common_1.toArray)(sheet.plain.text).forEach((t) => {
	        let { rot } = (0, common_1.parseRotation)(t._attributes.rot);
	        const { justify, mirror } = calculateJustifyAndMirrorForLabels(rot);
	        (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Label).id, {
	            type: 'text',
	            value: t._text,
	            size: parseFloat(t._attributes.size),
	            pos: [parseFloat(t._attributes.x), -parseFloat(t._attributes.y)],
	            angle: -rot,
	            justify,
	            thickness: 0,
	            mirror,
	            attr: [],
	            rejustifyOnRotate: true,
	        });
	    });
	    (0, Common_1.toArray)(sheet.plain.wire).forEach((w) => {
	        readWire(model, w);
	    });
	    const libs = (0, ObjMap_1.toMap)((0, Common_1.toArray)(schematicXml.libraries?.library), (l) => l._attributes.name);
	    const libDeviceSets = (0, ObjMap_1.toMap)((0, Common_1.toArray)(schematicXml.libraries?.library), (l) => l._attributes.name, (l) => (0, ObjMap_1.toMap)((0, Common_1.toArray)(l.devicesets?.deviceset), (d) => d._attributes.name));
	    for (const part of (0, Common_1.toArray)(schematicXml.parts.part)) {
	        const lib = libs[part._attributes.library];
	        if (!lib) {
	            model.warnings.push(`Could not find library '${part._attributes.library}' for part ${part._attributes.name}`);
	            continue;
	        }
	        for (const instance of (0, Common_1.toArray)(sheet.instances.instance).filter((i) => i._attributes.part === part._attributes.name)) {
	            const deviceSet = libDeviceSets[part._attributes.library][part._attributes.deviceset];
	            const device = (0, Common_1.toArray)(deviceSet?.devices.device).find((d) => d._attributes.name === part._attributes.device);
	            if (!device) {
	                model.warnings.push(`Could not find device '${part._attributes.device}' definition for ${part._attributes.name}`);
	                continue;
	            }
	            const rotation = (0, common_1.parseRotation)(instance._attributes.rot);
	            const value = part._attributes.value?.trim();
	            const module = {
	                ref: part._attributes.name,
	                footprintRef: `${device._attributes.package}::${instance._attributes.gate}`,
	                componentRef: '',
	                name: part._attributes.name,
	                center: [parseFloat(instance._attributes.x), -parseFloat(instance._attributes.y)],
	                layer: 'F',
	                drawProperties: {},
	                transform: {
	                    angle: -rotation.rot,
	                    pos: [0, 0],
	                    mirrored: rotation.mirrored,
	                },
	                pinNets: {},
	            };
	            const component = loadComponent(project, model, lib, libDeviceSets, module, part);
	            if (!component) {
	                model.warnings.push(`Could not load component for ${module.name}`);
	                break;
	            }
	            if (!component.symbols.find((s) => s.ref === module.footprintRef)) {
	                model.warnings.push(`Could not find symbol '${module.footprintRef}' for ${module.name}`);
	                break;
	            }
	            if (lib.description) {
	                if (!component.metadata) {
	                    component.metadata = {};
	                }
	                component.metadata['eagle.lib.description'] = lib.description._text;
	            }
	            if (part._attributes.name) {
	                module.drawProperties[PROP_NAME] = locateModuleProperty(module, { value: part._attributes.name });
	            }
	            if (part._attributes.value) {
	                module.drawProperties[PROP_VALUE] = locateModuleProperty(module, { value: part._attributes.value });
	            }
	            module.componentRef = component.name;
	            model.modules.push(module);
	            let metaModule = project.modules.find((m) => m.name === module.name);
	            if (!metaModule) {
	                metaModule = {
	                    name: module.name,
	                    instances: [],
	                    componentRef: component.name,
	                };
	                project.modules.push(metaModule);
	            }
	            if (!metaModule.name) {
	                metaModule.name = module.name;
	            }
	            if (value) {
	                if (metaModule.value && metaModule.value !== value) {
	                    model.warnings.push(`Module '${module.name}' has multiple values: '${metaModule.value}' and '${value}'`);
	                }
	                metaModule.value = value;
	            }
	            if (module.metadata && Object.keys(module.metadata).length > 0) {
	                if (!metaModule.metadata) {
	                    metaModule.metadata = {};
	                }
	                metaModule.metadata = {
	                    ...metaModule.metadata,
	                    ...module.metadata,
	                };
	            }
	            metaModule.instances.push({
	                ref: module.ref,
	                designId: model.designId,
	            });
	        }
	    }
	    const schematicNets = (0, Common_1.toArray)(sheet.nets.net);
	    schematicNets.forEach((net) => {
	        const netDefinition = model.nets[net._attributes.name] || parseNetDefinition(schematicNets, net, model.warnings);
	        (0, Common_1.toArray)(net.segment).forEach((segment) => {
	            (0, Common_1.toArray)(segment.pinref).forEach((pinRef) => {
	                const module = model.modules.find((m) => m.name === pinRef._attributes.part);
	                if (!module) {
	                    model.warnings.push(`Cannot find module for part: '${pinRef._attributes.part}'`);
	                    return;
	                }
	                const signalId = `${pinRef._attributes.gate}::${pinRef._attributes.pin}`;
	                module.pinNets[signalId] = netDefinition.name;
	                netDefinition.connections.push({
	                    moduleRef: pinRef._attributes.part,
	                    pinRef: signalId,
	                    moduleName: module.name,
	                });
	                if (!module.drawProperties['value']) {
	                    const component = project.components[module.componentRef];
	                    if (component.numPins === 1) {
	                        module.drawProperties[PROP_VALUE] = locateModuleProperty(module, {
	                            value: Object.values(module.pinNets)[0],
	                        });
	                    }
	                }
	            });
	            (0, Common_1.toArray)(segment.junction).forEach((j) => {
	                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Junction).id, {
	                    type: 'junction',
	                    position: [parseFloat(j._attributes.x), -parseFloat(j._attributes.y)],
	                    net: netDefinition.name,
	                });
	            });
	            (0, Common_1.toArray)(segment.wire).forEach((w) => {
	                readWire(model, w, net);
	            });
	            (0, Common_1.toArray)(segment.label).forEach((w) => {
	                const { rot } = (0, common_1.parseRotation)(w._attributes.rot);
	                const { justify, mirror } = calculateJustifyAndMirrorForLabels(rot);
	                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Label).id, {
	                    type: 'text',
	                    value: w._attributes.name || net._attributes.name,
	                    attr: [],
	                    justify,
	                    pos: [parseFloat(w._attributes.x), -parseFloat(w._attributes.y)],
	                    size: parseFloat(w._attributes.size),
	                    thickness: 1,
	                    angle: -rot,
	                    mirror,
	                    rejustifyOnRotate: true,
	                    net: net._attributes.name,
	                });
	            });
	        });
	        if (netDefinition.isGlobal) {
	            if (!project.globalNets[net._attributes.name]) {
	                project.globalNets[net._attributes.name] = {
	                    name: net._attributes.name,
	                };
	            }
	        }
	        model.nets[net._attributes.name] = netDefinition;
	    });
	    const bbox = new Area_1.default();
	    bbox.extendFromSchModel(project, model, project.layerSets[layers_1.EAGLE_SCH_LAYERSET], model.warnings);
	    model.edges_bbox = {
	        minx: bbox.xMin,
	        miny: bbox.yMin,
	        maxx: bbox.xMax,
	        maxy: bbox.yMax,
	    };
	    const visibleBbox = new Area_1.default();
	    visibleBbox.extendFromSchModel(project, model, project.layerSets[layers_1.EAGLE_SCH_LAYERSET], model.warnings, false);
	    model.initial_zoombox = {
	        minx: visibleBbox.xMin,
	        miny: visibleBbox.yMin,
	        maxx: visibleBbox.xMax,
	        maxy: visibleBbox.yMax,
	    };
	    (0, Util_1.dedupeWarnings)(model);
	    return model;
	}
	function parseNetDefinition(nets, net, warnings) {
	    const netInfo = {
	        name: net._attributes.name,
	        connections: [],
	        metadata: {
	            netclass: net._attributes.class,
	        },
	        isGlobal: false,
	    };
	    if (net._attributes.name?.toLowerCase().endsWith('_p') || net._attributes.name?.toLowerCase().endsWith('_n')) {
	        const pairName = net._attributes.name.slice(0, -2);
	        netInfo.diffPair = {
	            pairName,
	            positiveId: net._attributes.name?.toLowerCase().endsWith('_p') ?
	                net._attributes.name
	                : nets.find((n) => n._attributes.name.toLowerCase() === (pairName + '_P').toLowerCase())?._attributes
	                    .name,
	            negativeId: net._attributes.name?.toLowerCase().endsWith('_n') ?
	                net._attributes.name
	                : nets.find((n) => n._attributes.name.toLowerCase() === (pairName + '_N').toLowerCase())?._attributes
	                    .name,
	        };
	        if (!netInfo.diffPair.positiveId) {
	            warnings.push(`Could not find positive net for differential pair '${netInfo.diffPair.pairName}'`);
	            netInfo.diffPair.positiveId = pairName + '_P';
	        }
	        if (!netInfo.diffPair.negativeId) {
	            warnings.push(`Could not find negative net for differential pair '${netInfo.diffPair.pairName}'`);
	            netInfo.diffPair.negativeId = pairName + '_N';
	        }
	    }
	    return netInfo;
	}
	function readWire(model, w, net) {
	    const parsed = parseWire(w, net);
	    (0, schematicUtils_1.addSchematicDrawing)(model, parsed.layer, parsed.shape);
	}
	function parseWire(w, net) {
	    return {
	        layer: parseInt(w._attributes.layer),
	        shape: {
	            type: 'track',
	            start: [parseFloat(w._attributes.x1), -parseFloat(w._attributes.y1)],
	            end: [parseFloat(w._attributes.x2), -parseFloat(w._attributes.y2)],
	            width: 0,
	            net: net?._attributes.name,
	        },
	    };
	}
	function readLayers(xmlRoot) {
	    return (0, Common_1.toArray)(xmlRoot.eagle.drawing.layers.layer)
	        .map((l) => {
	        if (!(0, layers_1.isLayerActive)(l)) {
	            return;
	        }
	        return readLayer(l);
	    })
	        .filter((l) => !!l);
	}
	function readLayer(layer) {
	    const layerId = parseInt(layer._attributes.number);
	    const parsed = (0, layers_1.parseSchLayer)(layerId, layer._attributes.name);
	    if ((0, layers_1.isLayerVisible)(layer)) {
	        parsed.defaultVisible = true;
	    }
	    return parsed;
	}
	function loadComponent(project, model, lib, libDeviceSets, module, part) {
	    const deviceSet = libDeviceSets[part._attributes.library][part._attributes.deviceset];
	    const device = (0, Common_1.toArray)(deviceSet.devices.device).find((d) => d._attributes.name === part._attributes.device);
	    if (!device && part._attributes.device.length > 0) {
	        model.warnings.push(`Could not find device '${part._attributes.device}' definition for ${module.footprintRef}`);
	        return;
	    }
	    let footprint;
	    if (device && device._attributes.package) {
	        const devicePackage = (0, Common_1.toArray)(lib.packages.package).find((p) => p._attributes.name === device._attributes.package);
	        if (!devicePackage) {
	            model.warnings.push(`Could not find device package for ${module.footprintRef} (${device._attributes.package})`);
	            return;
	        }
	        footprint = (0, board_1.readFootprint)(devicePackage, device._attributes.name, part._attributes.library, module);
	    }
	    const gateMap = (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate), (g) => g._attributes.name, (g) => g._attributes.symbol);
	    const symbols = (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate).map((g) => getSymbol(lib, g._attributes.symbol)), (s) => s._attributes.name);
	    let componentName = getComponentName(part._attributes, deviceSet, device);
	    let component = project.components[componentName];
	    if (component) {
	        const missingSymbols = Object.values(symbols).find((s) => !component.symbols.find((existing) => existing.name === s._attributes.name));
	        if (!missingSymbols && (!footprint || component.footprints.find((fp) => fp.ref === footprint.ref))) {
	            return component;
	        }
	    }
	    else {
	        component = parseComponentDefinition(componentName, lib, device, deviceSet, symbols, footprint);
	    }
	    if (footprint && !component.footprints.find((fp) => fp.ref === footprint.ref)) {
	        component.footprints.push(footprint);
	        const padArr = (0, Common_1.toArray)(component.numPads);
	        padArr.push(footprint.pads.length);
	        component.numPads = padArr;
	    }
	    parseComponentSignals(component, deviceSet, symbols, gateMap, device, module, model.warnings);
	    project['components'][component.name] = component;
	    return component;
	}
	function getComponentName(objWithLib, deviceSet, device) {
	    const parts = [objWithLib.library, deviceSet._attributes.name];
	    if (device?._attributes.name) {
	        parts.push(device._attributes.name);
	    }
	    if (device?._attributes.package) {
	        parts.push(device._attributes.package);
	    }
	    return parts.join('::');
	}
	function parseComponentSignals(component, deviceSet, symbols, gateMap, device, module, warnings) {
	    for (const gate of parseGates(deviceSet.gates.gate)) {
	        const symbol = symbols[gate.symbol];
	        if (!symbol) {
	            warnings.push(`Could not find symbol with name ${gate.symbol} for deviceset ${deviceSet._attributes.name} gate ${gate.name}`);
	        }
	        component.symbols.push(loadSymbol(symbol, gate, device, warnings));
	    }
	    component.signals = parseGates(deviceSet.gates.gate).flatMap((gate) => {
	        const symbol = symbols[gateMap[gate.name]];
	        let canInferSignalType = !!symbol;
	        if (!canInferSignalType) {
	            warnings.push(`Could not find symbol '${gateMap[gate.name]}' for gate ${gate.name}` +
	                (!!module ? ` for part ${module.footprintRef}` : ''));
	        }
	        return (0, Common_1.toArray)(symbol.pin)
	            .map((p) => {
	            let blockInfo;
	            if (Object.entries(gateMap).length > 1) {
	                blockInfo = {
	                    index: gate.index,
	                    name: gate.name.replace(/^G\$(-|_|\W)*/gi, ''),
	                };
	            }
	            let signal;
	            if (!device?.connects?.connect) {
	                if (component.symbols.length === 1 && component.symbols[0].pins.length === 1) {
	                    signal = {
	                        id: `${gate.name}::${p._attributes.name}`,
	                        name: p._attributes.name,
	                        pinRefs: [`${gate.name}::${p._attributes.name}`],
	                        padRefs: [],
	                        blockInfo,
	                    };
	                }
	                else {
	                    return;
	                }
	            }
	            else {
	                const connects = (0, Common_1.toArray)(device?.connects?.connect).filter((c) => c._attributes.pin.split(' ').indexOf(p._attributes.name) !== -1);
	                signal = {
	                    id: `${gate.name}::${p._attributes.name}`,
	                    name: p._attributes.name,
	                    pinRefs: connects.flatMap((c) => c._attributes.pin.split(' ').map((p) => `${c._attributes.gate}::${p}`)),
	                    padRefs: connects.flatMap((c) => c._attributes.pad.split(' ')),
	                    blockInfo,
	                };
	            }
	            if (canInferSignalType) {
	                return inferSignalProperties(symbol, p, signal);
	            }
	            return signal;
	        })
	            .filter((s) => !!s);
	    });
	}
	function parseComponentDefinition(componentName, lib, device, deviceSet, symbols, footprint) {
	    const component = {
	        name: componentName,
	        signals: [],
	        footprints: [],
	        symbols: [],
	        numPins: 0,
	        numPads: 0,
	    };
	    (0, common_1.addMetadata)(component, 'eagle.library', lib._attributes?.name);
	    (0, common_1.addMetadata)(component, 'eagle.deviceset.name', deviceSet._attributes?.name);
	    (0, common_1.addMetadata)(component, 'eagle.deviceset.description', deviceSet.description?._text);
	    if (device?.connects) {
	        component.numPins = (0, Common_1.toArray)(device.connects?.connect).length;
	    }
	    else {
	        component.numPins = (0, Common_1.toArray)(deviceSet.gates.gate)
	            .map((gate) => {
	            const symbol = symbols[gate._attributes.symbol];
	            return (0, Common_1.toArray)(symbol.pin).length;
	        })
	            .reduce((p, c) => p + c, 0);
	    }
	    if (footprint) {
	        component.footprints.push(footprint);
	        component.numPads = component.footprints.map((f) => f.pads.length);
	    }
	    return component;
	}
	function inferSignalProperties(symbol, pin, signal) {
	    if (!signal.attributes) {
	        signal.attributes = {};
	    }
	    switch (pin._attributes.direction?.toLowerCase()) {
	        case PinDirection.BidirectionalIO:
	            signal.signalDirection = sigma_engine_shared_1.SignalDirection.BiDirectionalIO;
	            break;
	        case PinDirection.Input:
	            signal.signalDirection = sigma_engine_shared_1.SignalDirection.Input;
	            break;
	        case PinDirection.Output:
	            signal.signalDirection = sigma_engine_shared_1.SignalDirection.Output;
	            break;
	        case PinDirection.HiImpedence:
	            setParametric(signal, SignalParametrics.HiImpedence, true);
	            break;
	        case PinDirection.Passive:
	        case PinDirection.OpenCollectorOpenDrain:
	            break;
	        case PinDirection.NotConnected:
	            signal.attributes.noConnect = true;
	            break;
	        case PinDirection.Supply:
	            signal.attributes.power = true;
	            signal.signalDirection = sigma_engine_shared_1.SignalDirection.Output;
	            break;
	        default:
	        case PinDirection.Power:
	            inferSignalPowerAttributes(symbol, signal, pin);
	            break;
	    }
	    return signal;
	}
	function parseSchematicXml(schematic) {
	    const json = (0, xml_js_1.xml2json)(schematic, { compact: true });
	    return JSON.parse(json);
	}
	function getSymbol(lib, symbol) {
	    return (0, Common_1.toArray)(lib.symbols?.symbol).find((s) => s._attributes.name === symbol);
	}
	function loadSymbol(symbol, gate, device, warnings) {
	    const fp = {
	        name: symbol._attributes.name,
	        ref: `${device?._attributes.package}::${gate.name}`,
	        drawings: [],
	        pins: [],
	        metadata: {},
	    };
	    (0, Common_1.toArray)(symbol.pin).forEach((pin, no) => {
	        const start = [parseFloat(pin._attributes.x), -parseFloat(pin._attributes.y)];
	        const length = pinLengthToNumber(pin._attributes.length);
	        const rotation = (0, common_1.parseRotation)(pin._attributes.rot);
	        const drawing = {
	            type: 'pin',
	            ref: `${gate.name}::${pin._attributes.name}`,
	            name: pin._attributes.name,
	            number: no.toString(),
	            start,
	            end: (0, Transform_1.getLineOffset)(length, rotation.rot, start),
	            layer: (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Pin).id,
	            drawings: [],
	        };
	        if (pin._attributes.direction?.toLowerCase() === PinDirection.Supply) {
	            drawing.isNetLabel = true;
	        }
	        fp.pins.push(drawing);
	        switch ((pin._attributes.visible || PinVisibility.Both).toLowerCase()) {
	            case PinVisibility.Off:
	                break;
	            case PinVisibility.Both:
	                drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pin'));
	                if (device) {
	                    drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pad'));
	                }
	                break;
	            case PinVisibility.Pin:
	                drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pin'));
	                break;
	            case PinVisibility.Pad:
	                if (device) {
	                    drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pad'));
	                }
	                break;
	            default:
	                warnings.push(`Unknown pin visibility '${pin._attributes.visible}'`);
	                break;
	        }
	        if (drawing.drawings.length === 0) {
	            delete drawing.drawings;
	        }
	    });
	    (0, Common_1.toArray)(symbol.rectangle).forEach((rect) => {
	        const parsed = (0, board_1.readRectangle)(rect);
	        fp.drawings.push({
	            layers: [parsed.layer],
	            drawing: parsed.shape,
	        });
	    });
	    (0, Common_1.toArray)(symbol.circle).forEach((circ) => {
	        const parsed = (0, board_1.readCircle)(circ);
	        fp.drawings.push({
	            layers: [parsed.layer],
	            drawing: parsed.shape,
	        });
	    });
	    (0, Common_1.toArray)(symbol.wire).forEach((w) => {
	        const parsed = parseWire(w);
	        fp.drawings.push({
	            layers: [parsed.layer],
	            drawing: parsed.shape,
	        });
	    });
	    (0, Common_1.toArray)(symbol.text).forEach((t) => {
	        const parsed = (0, board_1.readText)(t, true);
	        parsed.shape.rejustifyOnRotate = true;
	        fp.drawings.push({
	            layers: [parsed.layer],
	            drawing: parsed.shape,
	        });
	    });
	    (0, Common_1.toArray)(symbol.polygon).forEach((p) => {
	        const parsed = (0, board_1.readPolygon)(p);
	        fp.drawings.push({
	            layers: [parseInt(p._attributes.layer)],
	            drawing: parsed,
	        });
	    });
	    return fp;
	}
	function parseSymbolPinText(pin, gate, device, parsedPin, rotation, type) {
	    const layer = (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.PinName);
	    const drawing = {
	        type: 'text',
	        value: '',
	        pos: parsedPin.end,
	        angle: (0, Transform_1.clampRotation)(rotation.rot),
	        mirror: rotation.mirrored,
	        attr: [],
	        justify: [-1, 0],
	        size: 1.778,
	        thickness: 1,
	        rejustifyOnRotate: true,
	    };
	    switch (type) {
	        case 'pin':
	            drawing.value = pin._attributes.name;
	            drawing.pos = (0, Transform_1.getLineOffset)(2.54, rotation.rot, parsedPin.end);
	            break;
	        case 'pad':
	            const connect = (0, Common_1.toArray)(device?.connects?.connect).find((c) => c._attributes.gate === gate.name && c._attributes.pin === pin._attributes.name);
	            if (connect) {
	                drawing.pos = (0, Transform_1.getLineOffset)(-0.5, -rotation.rot, parsedPin.end);
	                drawing.value = parsePadSymbolValue(connect._attributes.pad);
	                drawing.justify = [-drawing.justify[0], -1];
	            }
	            break;
	    }
	    if (drawing.angle >= 90 && drawing.angle < 180) {
	        drawing.justify[0] *= -1;
	    }
	    if (drawing.angle >= 180 && drawing.angle < 270) {
	        drawing.justify[1] *= -1;
	    }
	    if (drawing.angle >= 270) {
	        drawing.justify[0] *= -1;
	        drawing.justify[1] *= -1;
	    }
	    return {
	        layers: [layer.id],
	        drawing,
	    };
	}
	function parsePadSymbolValue(pad) {
	    const parts = pad.split(/\s/);
	    if (parts.length < 2) {
	        return pad;
	    }
	    return `${parts[0]}*${parts.length}`;
	}
	function setParametric(signal, parameter, value) {
	    if (!signal.parametrics) {
	        signal.parametrics = {};
	    }
	    signal.parametrics[parameter] = value;
	}
	function inferSignalPowerAttributes(symbol, signal, pin) {
	    const matches = new Map();
	    matches.set('ground', [
	        { match: 'gnd', dist: 1 },
	        { match: 'agnd', dist: 0 },
	    ]);
	    matches.set('power', [
	        { match: 'vcc', dist: 1 },
	        { match: 'vin', dist: 1 },
	        { match: 'vdd', dist: 1 },
	        { match: 'vdc', dist: 1 },
	        { match: 'avcc', dist: 0 },
	        { match: 'avdd', dist: 0 },
	    ]);
	    const pinName = pin._attributes.name?.toLowerCase();
	    for (const sigType of matches.keys()) {
	        for (const match of matches.get(sigType)) {
	            if (strDist.get(match.match, pinName) <= match.dist) {
	                if (!signal.attributes) {
	                    signal.attributes = {};
	                }
	                if (sigType === 'ground') {
	                    signal.attributes.ground = true;
	                }
	                else if (sigType === 'power') {
	                    signal.attributes.power = true;
	                }
	                return;
	            }
	        }
	    }
	}
	function pinLengthToNumber(length) {
	    const baseLen = 2.54;
	    switch (length) {
	        case PinLength.Middle:
	            return 2 * baseLen;
	        case PinLength.Short:
	            return baseLen;
	        case PinLength.Point:
	            return 0;
	        default:
	        case PinLength.Long:
	            return 3 * baseLen;
	    }
	}
	function locateModuleProperty(module, property) {
	    return {
	        ...property,
	        isPositionOffset: true,
	    };
	}
	function calculateJustifyAndMirrorForLabels(rot) {
	    const isUpsideDown = rot >= 90 && rot <= 180;
	    const mirror = isUpsideDown ? [true, true] : [false, false];
	    const justify = isUpsideDown ? [1, -1] : [-1, 1];
	    return { justify, mirror };
	}
	function parseGates(gate) {
	    return (0, Common_1.toArray)(gate).map((g, index) => {
	        return {
	            name: g._attributes.name,
	            symbol: g._attributes.symbol,
	            x: g._attributes.x,
	            y: g._attributes.y,
	            index,
	        };
	    });
	}
	
	return schematic;
}

var project$1 = {};

var hasRequiredProject$1;

function requireProject$1 () {
	if (hasRequiredProject$1) return project$1;
	hasRequiredProject$1 = 1;
	Object.defineProperty(project$1, "__esModule", { value: true });
	project$1.parseProject = parseProject;
	const board_1 = requireBoard();
	const schematic_1 = requireSchematic();
	const Font_1 = requireFont();
	async function parseProject(def) {
	    if (def.type !== 'eagle') {
	        throw new Error(`Attempted to parse non-eagle project def: ${def.type}`);
	    }
	    const project = {
	        type: def.type,
	        layerSets: {},
	        components: {},
	        designs: {},
	        modules: [],
	        warnings: [],
	        fonts: {
	            fonts: {},
	        },
	        globalNets: {},
	        processingResults: {
	            type: 'project-processing',
	            message: 'Project processed successfully',
	            innerResults: [],
	        },
	    };
	    if (def.name) {
	        project.name = def.name;
	    }
	    for (const [schId, schs] of Object.entries(def.schematics)) {
	        const parsed = (0, schematic_1.parseSchematic)(project, await schs.contents.text(), schId);
	        for (const parsedSch of parsed) {
	            project.designs[parsedSch.designId] = parsedSch;
	        }
	    }
	    for (const [brdId, brd] of Object.entries(def.boards)) {
	        const parsed = (0, board_1.parseBoard)(project, await brd.contents.text(), brdId);
	        project.designs[parsed.designId] = parsed;
	    }
	    (0, Font_1.packModelFonts)(project);
	    return project;
	}
	
	return project$1;
}

var library = {};

var hasRequiredLibrary;

function requireLibrary () {
	if (hasRequiredLibrary) return library;
	hasRequiredLibrary = 1;
	Object.defineProperty(library, "__esModule", { value: true });
	library.parseLibraryComponents = parseLibraryComponents;
	library.parseLibraryXml = parseLibraryXml;
	const xml_js_1 = requireLib$1z();
	const board_1 = requireBoard();
	const schematic_1 = requireSchematic();
	const schematic_2 = requireSchematic();
	const Common_1 = requireCommon();
	const ObjMap_1 = requireObjMap();
	const Utils_1 = requireUtils$4();
	function parseLibraryComponents(libraryContents, libName) {
	    const xmlRoot = parseLibraryXml(libraryContents);
	    const libXml = xmlRoot.eagle.drawing.library;
	    const libDeviceSets = (0, Common_1.toArray)(libXml).map((l) => [
	        l,
	        (0, Common_1.toArray)(l.devicesets?.deviceset),
	    ]);
	    const warnings = [];
	    const components = new Map();
	    for (const [lib, deviceSets] of libDeviceSets) {
	        for (const deviceSet of deviceSets) {
	            for (const device of (0, Common_1.toArray)(deviceSet.devices.device)) {
	                const cpName = (0, schematic_1.getComponentName)({ library: lib._attributes?.name || libName }, deviceSet, device);
	                let component = components.get(cpName);
	                (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate).map((g) => (0, schematic_1.getSymbol)(lib, g._attributes.symbol)), (s) => s._attributes.name);
	                const gateMap = (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate), (g) => g._attributes.name, (g) => g._attributes.symbol);
	                if (!component) {
	                    const symbols = (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate).map((g) => (0, schematic_1.getSymbol)(lib, g._attributes.symbol)), (s) => s._attributes.name);
	                    component = (0, schematic_1.parseComponentDefinition)(cpName, lib, device, deviceSet, symbols, undefined);
	                    if (!component) {
	                        warnings.push(`Could not parse component definition for ${cpName}`);
	                        continue;
	                    }
	                    components.set(cpName, component);
	                    (0, schematic_2.parseComponentSignals)(component, deviceSet, symbols, gateMap, device, undefined, warnings);
	                }
	                const devicePackage = (0, Common_1.toArray)(lib.packages.package).find((p) => p._attributes.name === device._attributes.package);
	                if (!devicePackage) {
	                    warnings.push(`Could not find device package (${device._attributes.package})`);
	                    continue;
	                }
	                const footprint = (0, board_1.readFootprint)(devicePackage, device._attributes.name, lib._attributes?.name || libName, undefined);
	                component.footprints.push(footprint);
	                component.numPads = footprint.pads.length;
	                if (component) {
	                    component.numPads = (0, Utils_1.maxA)(...component.footprints.map((f) => f.pads.length));
	                }
	            }
	        }
	    }
	    return {
	        components: Array.from(components.values()),
	        warnings,
	    };
	}
	function parseLibraryXml(lib) {
	    const json = (0, xml_js_1.xml2json)(lib, { compact: true });
	    return JSON.parse(json);
	}
	
	return library;
}

var hasRequiredEagle;

function requireEagle () {
	if (hasRequiredEagle) return Eagle;
	hasRequiredEagle = 1;
	(function (exports) {
		var __createBinding = (Eagle && Eagle.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Eagle && Eagle.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBoard(), exports);
		__exportStar(requireSchematic(), exports);
		__exportStar(requireProject$1(), exports);
		__exportStar(requireLibrary(), exports);
		__exportStar(requireLayers$1(), exports);
		
	} (Eagle));
	return Eagle;
}

var Altium = {};

var altiumRequiredFiles = {};

var hasRequiredAltiumRequiredFiles;

function requireAltiumRequiredFiles () {
	if (hasRequiredAltiumRequiredFiles) return altiumRequiredFiles;
	hasRequiredAltiumRequiredFiles = 1;
	Object.defineProperty(altiumRequiredFiles, "__esModule", { value: true });
	altiumRequiredFiles.checkAltiumRequiredFiles = checkAltiumRequiredFiles;
	const sigma_engine_shared_1 = requireDist$1();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	const altiumValidation_1 = requireAltiumValidation();
	const altiumProjectFileUtil_1 = requireAltiumProjectFileUtil();
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const project_detection_1 = requireProjectDetection();
	function getParsedProjectFile(detail) {
	    if (detail.cadTool !== 'altium' || detail.semanticType !== 'project' || !detail.fileValidationResult?.isValid) {
	        return undefined;
	    }
	    const value = detail.fileValidationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.Project];
	    if (typeof value !== 'object' || value === null) {
	        return undefined;
	    }
	    return value;
	}
	function getParsedStructureFile(detail) {
	    if (detail.cadTool !== 'altium' || !detail.fileValidationResult?.isValid) {
	        return undefined;
	    }
	    const value = detail.fileValidationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.ProjectStructure];
	    if (typeof value !== 'object' || value === null) {
	        return undefined;
	    }
	    return value;
	}
	function uploadHasSheetSymbols(fileDetails) {
	    return fileDetails.some((detail) => detail.cadTool === 'altium' &&
	        detail.semanticType === 'schematic' &&
	        (detail.referencedFiles?.some((ref) => ref.trim() !== '') ?? false));
	}
	function checkProjectStructureRule(context) {
	    if (context.files.some((f) => (0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.ProjectStructure)(f.relativePath))) {
	        return [];
	    }
	    const hasSchematics = context.fileDetails.some((detail) => detail.cadTool === 'altium' && detail.semanticType === 'schematic');
	    if (!hasSchematics) {
	        return [];
	    }
	    const results = [];
	    for (const detail of context.fileDetails) {
	        const projectFile = getParsedProjectFile(detail);
	        if (!projectFile) {
	            continue;
	        }
	        const util = new altiumProjectFileUtil_1.AltiumProjectFileUtil(projectFile);
	        const configured = util.getDesignConfiguration('HierarchyMode');
	        const resolved = configured === altiumProjectFileTypes_1.HierarchyMode.Automatic && uploadHasSheetSymbols(context.fileDetails) ?
	            altiumProjectFileTypes_1.HierarchyMode.Hierarchical
	            : configured;
	        if (!util.checkHierarchyModeRequiresProjectStructure(resolved)) {
	            continue;
	        }
	        results.push({
	            type: 'required-file-check',
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            message: `"${detail.relativePath}" is a hierarchical design (hierarchy mode ${altiumProjectFileTypes_1.HierarchyMode[resolved]}), which requires a ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file that is not in the uploaded files`,
	            rule: 'altium-project-structure',
	            cadTool: 'altium',
	            requiredFileExt: altiumFileTypes_1.AltiumFileType.ProjectStructure,
	            triggeringFile: detail.relativePath,
	            errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.MissingRequiredFile,
	        });
	    }
	    return results;
	}
	function normalizeSheetName(value) {
	    return sigma_engine_shared_1.Files.basenamePath((0, project_detection_1.normalizePathForCompare)(value.trim())).trim();
	}
	function addSchematicName(target, value) {
	    if (value === undefined) {
	        return;
	    }
	    const normalized = normalizeSheetName(value);
	    if (normalized === '' || !(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.SchDoc)(normalized)) {
	        return;
	    }
	    if (!target.has(normalized)) {
	        target.set(normalized, sigma_engine_shared_1.Files.basenamePath((0, project_detection_1.toForwardSlash)(value.trim())).trim());
	    }
	}
	function collectStructureSchematics(structure) {
	    const sheets = new Map();
	    for (const record of structure.topLevelDocuments ?? []) {
	        addSchematicName(sheets, record.FILENAME);
	    }
	    for (const record of structure.sheetSymbols ?? []) {
	        addSchematicName(sheets, record.FILENAME);
	    }
	    for (const record of structure.noMainPathDocuments ?? []) {
	        addSchematicName(sheets, record.FILENAME);
	    }
	    return sheets;
	}
	function collectProjectSchematics(projectFile) {
	    const sheets = new Map();
	    for (const document of projectFile.Document ?? []) {
	        addSchematicName(sheets, document.DocumentPath);
	    }
	    return sheets;
	}
	function findBoundProjectDetail(structurePath, fileDetails) {
	    const structureDir = (0, project_detection_1.dirnameForward)(structurePath);
	    let boundProject;
	    let boundHomeDirLength = -1;
	    for (const detail of fileDetails) {
	        if (!(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.Project)(detail.relativePath)) {
	            continue;
	        }
	        const projectHomeDir = (0, project_detection_1.dirnameForward)(detail.relativePath);
	        if ((0, project_detection_1.isSameOrDescendantDir)(structureDir, projectHomeDir) && projectHomeDir.length > boundHomeDirLength) {
	            boundProject = detail;
	            boundHomeDirLength = projectHomeDir.length;
	        }
	    }
	    return boundProject;
	}
	function hasMismatchedProjectStem(structurePath, projectPath) {
	    return (0, project_detection_1.basenameWithoutExtension)(structurePath).toLowerCase() !== (0, project_detection_1.basenameWithoutExtension)(projectPath).toLowerCase();
	}
	function mismatchedProjectStemDiagnosis(structurePath, projectPath) {
	    return `"${structurePath}" appears to belong to a different project or an older snapshot than "${projectPath}". REMOVE the stale ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file, rename it to match the project, or re-export the project so Altium regenerates it`;
	}
	function checkStructureConsistencyRule(context) {
	    const hasSchematics = context.fileDetails.some((detail) => detail.cadTool === 'altium' && detail.semanticType === 'schematic');
	    if (!hasSchematics) {
	        return [];
	    }
	    const results = [];
	    for (const detail of context.fileDetails) {
	        if (!(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.ProjectStructure)(detail.relativePath)) {
	            continue;
	        }
	        const structure = getParsedStructureFile(detail);
	        if (!structure) {
	            continue;
	        }
	        const projectDetail = findBoundProjectDetail(detail.relativePath, context.fileDetails);
	        if (!projectDetail) {
	            results.push({
	                type: 'structure-consistency-check',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: `"${detail.relativePath}" belongs to a project that is not in the upload; remove it, or upload its project`,
	                rule: 'altium-structure-consistency',
	                cadTool: 'altium',
	                structureFile: detail.relativePath,
	                attributedFilePath: detail.relativePath,
	                missingFromStructure: [],
	                unknownToProject: [],
	                errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.UnpairedStructureFile,
	            });
	            continue;
	        }
	        const projectFile = getParsedProjectFile(projectDetail);
	        if (!projectFile) {
	            continue;
	        }
	        const projectSheets = collectProjectSchematics(projectFile);
	        const structureSheets = collectStructureSchematics(structure);
	        const missingFromStructure = [...projectSheets]
	            .filter(([normalized]) => !structureSheets.has(normalized))
	            .map(([, displayName]) => displayName);
	        const unknownToProject = [...structureSheets]
	            .filter(([normalized]) => !projectSheets.has(normalized))
	            .map(([, displayName]) => displayName);
	        const nameMismatch = hasMismatchedProjectStem(detail.relativePath, projectDetail.relativePath);
	        if (missingFromStructure.length === 0 && unknownToProject.length === 0 && !nameMismatch) {
	            continue;
	        }
	        let severity;
	        let message;
	        if (missingFromStructure.length > 0) {
	            const problems = [
	                `the project lists schematic sheets the structure file does not name (${missingFromStructure.join(', ')})`,
	            ];
	            if (unknownToProject.length > 0) {
	                problems.push(`the structure file names schematic sheets the project does not list (${unknownToProject.join(', ')})`);
	            }
	            severity = sigma_engine_shared_1.BoundaryLevel.Error;
	            message = `"${detail.relativePath}" is out of sync with "${projectDetail.relativePath}": ${problems.join('; ')}. Re-save or re-export the project in Altium so the ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file is regenerated in sync with the project`;
	        }
	        else {
	            severity = sigma_engine_shared_1.BoundaryLevel.Warning;
	            message =
	                unknownToProject.length > 0 ?
	                    `"${detail.relativePath}" names schematic sheets that "${projectDetail.relativePath}" does not list (${unknownToProject.join(', ')}). These may be device sheets or managed sheets not listed in the project file; verify the ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file matches the uploaded project`
	                    : `"${detail.relativePath}" otherwise matches "${projectDetail.relativePath}"`;
	        }
	        if (nameMismatch) {
	            message = `${message}. ${mismatchedProjectStemDiagnosis(detail.relativePath, projectDetail.relativePath)}`;
	        }
	        results.push({
	            type: 'structure-consistency-check',
	            severity,
	            message,
	            rule: 'altium-structure-consistency',
	            cadTool: 'altium',
	            structureFile: detail.relativePath,
	            attributedFilePath: detail.relativePath,
	            projectFile: projectDetail.relativePath,
	            missingFromStructure,
	            unknownToProject,
	            errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.InconsistentStructureFile,
	        });
	    }
	    return results;
	}
	function checkAnnotationRule(context) {
	    if (context.missingReferencedFiles.length === 0) {
	        return [];
	    }
	    const missing = new Set(context.missingReferencedFiles);
	    const results = [];
	    for (const detail of context.fileDetails) {
	        if (!getParsedProjectFile(detail)) {
	            continue;
	        }
	        for (const ref of detail.referencedFiles ?? []) {
	            if (!(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.Annotation)(ref) || !missing.has(ref)) {
	                continue;
	            }
	            results.push({
	                type: 'required-file-check',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: `"${detail.relativePath}" was re-annotated using "${ref}", which is not in the uploaded files`,
	                rule: 'altium-annotation',
	                cadTool: 'altium',
	                requiredFileExt: altiumFileTypes_1.AltiumFileType.Annotation,
	                triggeringFile: detail.relativePath,
	                errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.MissingRequiredFile,
	            });
	        }
	    }
	    return results;
	}
	function checkAltiumRequiredFiles(context) {
	    return [
	        ...checkProjectStructureRule(context),
	        ...checkStructureConsistencyRule(context),
	        ...checkAnnotationRule(context),
	    ];
	}
	
	return altiumRequiredFiles;
}

var project = {};

var hasRequiredProject;

function requireProject () {
	if (hasRequiredProject) return project;
	hasRequiredProject = 1;
	(function (exports) {
		var __createBinding = (project && project.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (project && project.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAltiumProjectFileUtil(), exports);
		__exportStar(requireAltiumProjectFileTypes(), exports);
		__exportStar(requireProjectFileParser(), exports);
		
	} (project));
	return project;
}

var parseAltiumProject = {};

var AltiumHierarchicalSchematicProcessor = {};

var AltiumSchematicTransformer = {};

var AltiumBusRange = {};

var hasRequiredAltiumBusRange;

function requireAltiumBusRange () {
	if (hasRequiredAltiumBusRange) return AltiumBusRange;
	hasRequiredAltiumBusRange = 1;
	Object.defineProperty(AltiumBusRange, "__esModule", { value: true });
	AltiumBusRange.AltiumBusRange = void 0;
	class AltiumBusRange$1 {
	    static { this.MAX_WIDTH = 4096; }
	    static parse(text) {
	        const match = /^(.*?)\[\s*(\d+)\s*\.\.\s*(\d+)\s*\]$/.exec(text.trim());
	        if (!match) {
	            return undefined;
	        }
	        const prefix = match[1];
	        const from = parseInt(match[2], 10);
	        const to = parseInt(match[3], 10);
	        if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || Math.abs(to - from) >= AltiumBusRange$1.MAX_WIDTH) {
	            return undefined;
	        }
	        const step = from <= to ? 1 : -1;
	        const members = [];
	        for (let i = from; step > 0 ? i <= to : i >= to; i += step) {
	            members.push(`${prefix}${i}`);
	        }
	        return { declaration: text.trim(), prefix, members };
	    }
	}
	AltiumBusRange.AltiumBusRange = AltiumBusRange$1;
	
	return AltiumBusRange;
}

var AltiumShapeConverters = {};

var layers = {};

var hasRequiredLayers;

function requireLayers () {
	if (hasRequiredLayers) return layers;
	hasRequiredLayers = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AltiumSchLayerMap = exports.AltiumSpecificSchLayerTypes = exports.AltiumSchLayerSelector = exports.ALTIUM_SCH_LAYERSET = exports.ALTIUM_BRD_LAYERSET = void 0;
		exports.getAltiumDefaultLayerSets = getAltiumDefaultLayerSets;
		exports.getAltiumSchLayerByRecordType = getAltiumSchLayerByRecordType;
		exports.getAltiumSchLayerByType = getAltiumSchLayerByType;
		exports.getAltiumSchLayers = getAltiumSchLayers;
		const sigma_engine_shared_1 = requireDist$1();
		const SchDocTypes_1 = requireSchDocTypes();
		const { toArray } = sigma_engine_shared_1.Utils;
		const { PcbLayerType, SchLayerType } = sigma_engine_shared_1.DesignModels;
		exports.ALTIUM_BRD_LAYERSET = 'altiumbrd';
		exports.ALTIUM_SCH_LAYERSET = 'altiumsch';
		class AltiumLayerSelector {
		    filterLayers(name) {
		        return this.getLayersByName(name);
		    }
		    getLayerByRecordType(recordType, defaultType) {
		        return this.getLayerByType(defaultType);
		    }
		    getLayerByType(layer) {
		        const layers = this.getLayersByType(layer);
		        if (layers) {
		            return layers[0];
		        }
		        return this.getLayerByType(sigma_engine_shared_1.DesignModels.UnknownLayerType.Other);
		    }
		    getLayers() {
		        return Array.from(this.enumerateLayers());
		    }
		}
		class AltiumSchLayerSelector extends AltiumLayerSelector {
		    getLayersByName(name) {
		        return toArray(Array.from(exports.AltiumSchLayerMap.values()).find((l) => l.name === name));
		    }
		    getLayersByType(type) {
		        return toArray(exports.AltiumSchLayerMap.get(type));
		    }
		    enumerateLayers() {
		        return exports.AltiumSchLayerMap.values();
		    }
		}
		exports.AltiumSchLayerSelector = AltiumSchLayerSelector;
		function getAltiumDefaultLayerSets() {
		    return {
		        [exports.ALTIUM_SCH_LAYERSET]: {
		            type: 'layerSet',
		            layerSetType: 'schematic',
		            id: exports.ALTIUM_SCH_LAYERSET,
		            layers: new AltiumSchLayerSelector().getLayers().map((l) => {
		                l.defaultVisible = true;
		                return l;
		            }),
		        },
		    };
		}
		function getAltiumSchLayerByRecordType(recordType, defaultType = SchLayerType.Other) {
		    switch (recordType) {
		        case SchDocTypes_1.RecordType.WIRE:
		            return getAltiumSchLayerByType(SchLayerType.Wire);
		        case SchDocTypes_1.RecordType.BUS:
		            return getAltiumSchLayerByType(SchLayerType.Bus);
		        case SchDocTypes_1.RecordType.SIGNAL_HARNESS:
		            return getAltiumSchLayerByType(SchLayerType.Bus);
		        case SchDocTypes_1.RecordType.JUNCTION:
		            return getAltiumSchLayerByType(SchLayerType.Junction);
		        case SchDocTypes_1.RecordType.BUS_ENTRY:
		            return getAltiumSchLayerByType(SchLayerType.BusJunction);
		        case SchDocTypes_1.RecordType.LABEL:
		            return getAltiumSchLayerByType(SchLayerType.Label);
		        case SchDocTypes_1.RecordType.NET_LABEL:
		            return getAltiumSchLayerByType(SchLayerType.Label);
		        case SchDocTypes_1.RecordType.PIN:
		            return getAltiumSchLayerByType(SchLayerType.Pin);
		        case SchDocTypes_1.RecordType.SHEET_SYMBOL:
		            return getAltiumSchLayerByType(SchLayerType.Symbol);
		        case SchDocTypes_1.RecordType.HARNESS_CONNECTOR:
		            return getAltiumSchLayerByType(SchLayerType.Symbol);
		        case SchDocTypes_1.RecordType.HARNESS_ENTRY:
		            return getAltiumSchLayerByType(SchLayerType.Symbol);
		        case SchDocTypes_1.RecordType.HARNESS_TYPE:
		            return getAltiumSchLayerByType(SchLayerType.Label);
		        case SchDocTypes_1.RecordType.SHEET_ENTRY:
		            return getAltiumSchLayerByType(SchLayerType.Pin);
		        case SchDocTypes_1.RecordType.POWER_PORT:
		            return getAltiumSchLayerByType(SchLayerType.Symbol);
		        case SchDocTypes_1.RecordType.PORT:
		            return getAltiumSchLayerByType(SchLayerType.Symbol);
		        case SchDocTypes_1.RecordType.COMPONENT:
		            return getAltiumSchLayerByType(SchLayerType.Symbol);
		        case SchDocTypes_1.RecordType.TEXT_FRAME:
		            return getAltiumSchLayerByType(SchLayerType.Label);
		        case SchDocTypes_1.RecordType.NOTE:
		            return getAltiumSchLayerByType(SchLayerType.Label);
		        case SchDocTypes_1.RecordType.DESIGNATOR:
		            return getAltiumSchLayerByType(SchLayerType.NameLabel);
		        case SchDocTypes_1.RecordType.PARAMETER:
		            return getAltiumSchLayerByType(SchLayerType.ValueLabel);
		        case SchDocTypes_1.RecordType.IMPLEMENTATION_LIST:
		            return getAltiumSchLayerByType(SchLayerType.Other);
		    }
		    return getAltiumSchLayerByType(defaultType);
		}
		function getAltiumSchLayerByType(layer) {
		    return exports.AltiumSchLayerMap.get(layer);
		}
		function getAltiumSchLayers() {
		    return Array.from(exports.AltiumSchLayerMap.values());
		}
		var AltiumSpecificSchLayerTypes;
		(function (AltiumSpecificSchLayerTypes) {
		    AltiumSpecificSchLayerTypes["Drawings"] = "Drawings";
		    AltiumSpecificSchLayerTypes["TitleBlock"] = "TitleBlock";
		})(AltiumSpecificSchLayerTypes || (exports.AltiumSpecificSchLayerTypes = AltiumSpecificSchLayerTypes = {}));
		exports.AltiumSchLayerMap = new Map([
		    [SchLayerType.Label, { id: 1, type: SchLayerType.Label, name: 'Label' }],
		    [SchLayerType.NameLabel, { id: 2, type: SchLayerType.NameLabel, name: 'NameLabel' }],
		    [SchLayerType.ValueLabel, { id: 3, type: SchLayerType.ValueLabel, name: 'ValueLabel' }],
		    [SchLayerType.Pin, { id: 4, type: SchLayerType.Pin, name: 'Pin' }],
		    [SchLayerType.PinName, { id: 5, type: SchLayerType.PinName, name: 'PinName' }],
		    [SchLayerType.PinNumber, { id: 6, type: SchLayerType.PinNumber, name: 'PinNumber' }],
		    [SchLayerType.Symbol, { id: 7, type: SchLayerType.Symbol, name: 'Symbol' }],
		    [SchLayerType.Wire, { id: 8, type: SchLayerType.Wire, name: 'Wire' }],
		    [SchLayerType.Bus, { id: 9, type: SchLayerType.Bus, name: 'Bus' }],
		    [SchLayerType.Junction, { id: 10, type: SchLayerType.Junction, name: 'Junction' }],
		    [SchLayerType.BusJunction, { id: 11, type: SchLayerType.BusJunction, name: 'BusJunction' }],
		    [SchLayerType.Other, { id: 12, type: SchLayerType.Other, name: 'Other' }],
		    [SchLayerType.Background, { id: 13, type: SchLayerType.Background, name: 'Background Contrast' }],
		    [AltiumSpecificSchLayerTypes.Drawings, { id: 14, type: SchLayerType.Other, name: 'Drawings' }],
		    [AltiumSpecificSchLayerTypes.TitleBlock, { id: 15, type: SchLayerType.Other, name: 'TitleBlock' }],
		    [SchLayerType.Image, { id: 16, type: SchLayerType.Image, name: 'Image' }],
		]);
		
	} (layers));
	return layers;
}

var hasRequiredAltiumShapeConverters;

function requireAltiumShapeConverters () {
	if (hasRequiredAltiumShapeConverters) return AltiumShapeConverters;
	hasRequiredAltiumShapeConverters = 1;
	Object.defineProperty(AltiumShapeConverters, "__esModule", { value: true });
	AltiumShapeConverters.AltiumShapeConverters = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumEnums_1 = requireAltiumEnums();
	const layers_1 = requireLayers();
	const CoordinateConverter_1 = requireCoordinateConverter();
	const PropertyParser_1 = requirePropertyParser();
	class AltiumShapeConverters$1 {
	    static { this.LineWidthFactor = 10; }
	    static { this.PinDesignatorBodyInset = CoordinateConverter_1.CoordinateConverter.milsToMm(50); }
	    static { this.HarnessBraceMaximumDepth = CoordinateConverter_1.CoordinateConverter.milsToMm(200); }
	    static { this.HarnessBraceArcSampleCount = 6; }
	    static { this.HarnessEntryInset = CoordinateConverter_1.CoordinateConverter.milsToMm(50); }
	    static { this.HarnessEntryMarkerRadius = CoordinateConverter_1.CoordinateConverter.milsToMm(7); }
	    static { this.Logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)(); }
	    static convertLine(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        return {
	            type: 'segment',
	            start: [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(record.startLocation.x),
	                ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.startLocation.y),
	            ],
	            end: [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(record.endLocation.x),
	                ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.endLocation.y),
	            ],
	            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertRectangle(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        return {
	            type: 'rect',
	            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.topLeft.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.topLeft.y)],
	            end: [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(record.bottomRight.x),
	                ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.bottomRight.y),
	            ],
	            strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * 10,
	            filled: record.isSolid,
	        };
	    }
	    static convertToColorOverride(fillColor) {
	        return [fillColor.r, fillColor.g, fillColor.b, 1];
	    }
	    static convertPolyline(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        return {
	            type: 'polygon',
	            points: record.points.map((p) => [CoordinateConverter_1.CoordinateConverter.milsToMm(p.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(p.y)]),
	            filled: false,
	            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertBus(record) {
	        const points = record.points.map((p) => [
	            CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
	            CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
	        ]);
	        const buses = [];
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor * 2;
	        for (let i = 0; i < points.length - 1; i++) {
	            buses.push({
	                type: 'bus',
	                start: points[i],
	                end: points[i + 1],
	                width: lineWidth,
	            });
	        }
	        return buses;
	    }
	    static convertSignalHarness(record) {
	        const points = record.points.map((p) => [
	            CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
	            CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
	        ]);
	        const harnesses = [];
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor * 2;
	        for (let i = 0; i < points.length - 1; i++) {
	            harnesses.push({
	                type: 'bus',
	                start: points[i],
	                end: points[i + 1],
	                width: lineWidth,
	                colorOverride: AltiumShapeConverters$1.convertToColorOverride(record.color),
	            });
	        }
	        return harnesses;
	    }
	    static convertHarnessConnector(record) {
	        if (!Number.isFinite(record.xSize) || record.xSize <= 0 || !Number.isFinite(record.ySize) || record.ySize <= 0) {
	            return undefined;
	        }
	        const x = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
	        const y = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
	        const width = CoordinateConverter_1.CoordinateConverter.milsToMm(record.xSize);
	        const height = CoordinateConverter_1.CoordinateConverter.milsToMm(record.ySize);
	        const sideGeometry = AltiumShapeConverters$1.getHarnessSideGeometry(record.side);
	        const rawPrimaryPosition = CoordinateConverter_1.CoordinateConverter.milsToMm(record.primaryConnectionPosition);
	        const primaryPosition = Number.isFinite(rawPrimaryPosition) ? rawPrimaryPosition : (sideGeometry.isVerticalSide ? height : width) / 2;
	        const braceDepth = AltiumShapeConverters$1.calculateHarnessBraceDepth(record);
	        let firstInnerCorner;
	        let secondInnerCorner;
	        let farCorners;
	        const tip = [
	            x + sideGeometry.outerEdgeRatio[0] * width + sideGeometry.tangent[0] * primaryPosition,
	            y + sideGeometry.outerEdgeRatio[1] * height + sideGeometry.tangent[1] * primaryPosition,
	        ];
	        switch (record.side) {
	            case AltiumEnums_1.SheetSymbolSide.Right: {
	                const innerX = x + width - braceDepth;
	                firstInnerCorner = [innerX, y];
	                secondInnerCorner = [innerX, y + height];
	                farCorners = [
	                    [x, y],
	                    [x, y + height],
	                ];
	                break;
	            }
	            case AltiumEnums_1.SheetSymbolSide.Top: {
	                const innerY = y + braceDepth;
	                firstInnerCorner = [x, innerY];
	                secondInnerCorner = [x + width, innerY];
	                farCorners = [
	                    [x, y + height],
	                    [x + width, y + height],
	                ];
	                break;
	            }
	            case AltiumEnums_1.SheetSymbolSide.Bottom: {
	                const innerY = y + height - braceDepth;
	                firstInnerCorner = [x, innerY];
	                secondInnerCorner = [x + width, innerY];
	                farCorners = [
	                    [x, y],
	                    [x + width, y],
	                ];
	                break;
	            }
	            case AltiumEnums_1.SheetSymbolSide.Left:
	            default: {
	                const innerX = x + braceDepth;
	                firstInnerCorner = [innerX, y];
	                secondInnerCorner = [innerX, y + height];
	                farCorners = [
	                    [x + width, y],
	                    [x + width, y + height],
	                ];
	                break;
	            }
	        }
	        const colorOverride = AltiumShapeConverters$1.convertToColorOverride(record.color);
	        const braceWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor;
	        const firstArm = AltiumShapeConverters$1.sampleHarnessBraceArm(firstInnerCorner, tip, record.side, braceDepth);
	        const secondArm = AltiumShapeConverters$1.sampleHarnessBraceArm(secondInnerCorner, tip, record.side, braceDepth);
	        const drawings = [];
	        if (record.isSolid) {
	            drawings.push({
	                type: 'polygon',
	                points: [farCorners[0], ...firstArm, ...secondArm.toReversed(), farCorners[1]],
	                filled: true,
	                width: 0,
	                colorOverride: AltiumShapeConverters$1.convertToColorOverride(record.areaColor),
	            });
	        }
	        drawings.push({
	            type: 'polygon',
	            points: firstArm,
	            filled: false,
	            width: braceWidth,
	            colorOverride,
	        }, {
	            type: 'polygon',
	            points: secondArm,
	            filled: false,
	            width: braceWidth,
	            colorOverride,
	        });
	        return { type: 'group', pos: [0, 0], drawings };
	    }
	    static convertHarnessEntryText(record, entryPosition, sheet, projectFontId, options) {
	        if (record.name.length === 0) {
	            return undefined;
	        }
	        const sideGeometry = AltiumShapeConverters$1.getHarnessSideGeometry(record.side);
	        const pos = [
	            entryPosition[0] - sideGeometry.outward[0] * AltiumShapeConverters$1.HarnessEntryInset,
	            entryPosition[1] - sideGeometry.outward[1] * AltiumShapeConverters$1.HarnessEntryInset,
	        ];
	        const justifyDirection = sideGeometry.isVerticalSide ? sideGeometry.outward[0] : -sideGeometry.outward[1];
	        const justify = [justifyDirection, 0];
	        const angle = sideGeometry.isVerticalSide ? 0 : 90;
	        const resolvedFont = AltiumShapeConverters$1.resolveHarnessFont(sheet, record.textFontId, projectFontId, options);
	        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms({
	            type: 'text',
	            pos,
	            value: record.name,
	            angle,
	            thickness: 1,
	            justify,
	            ...resolvedFont.fontInfo.textOpts,
	            font: resolvedFont.projectFontId,
	            colorOverride: AltiumShapeConverters$1.convertToColorOverride(record.textColor),
	        });
	    }
	    static convertHarnessEntryMarker(record, entryPosition) {
	        return {
	            type: 'circle',
	            center: entryPosition,
	            radius: AltiumShapeConverters$1.HarnessEntryMarkerRadius,
	            filled: true,
	            colorOverride: AltiumShapeConverters$1.convertToColorOverride(record.color),
	        };
	    }
	    static convertHarnessType(record, fallbackPosition, sheet, projectFontId, options) {
	        if (record.isHidden) {
	            return undefined;
	        }
	        const hasStoredLocation = record.properties['location.x'] !== undefined && record.properties['location.y'] !== undefined;
	        const pos = hasStoredLocation ?
	            [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)]
	            : fallbackPosition;
	        const resolvedFont = AltiumShapeConverters$1.resolveHarnessFont(sheet, record.fontId, projectFontId, options);
	        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms({
	            type: 'text',
	            pos,
	            value: record.text,
	            thickness: 1,
	            justify: [-1, 1],
	            ...resolvedFont.fontInfo.textOpts,
	            font: resolvedFont.projectFontId,
	        });
	    }
	    static calculateHarnessBraceDepth(record) {
	        const width = CoordinateConverter_1.CoordinateConverter.milsToMm(record.xSize);
	        const height = CoordinateConverter_1.CoordinateConverter.milsToMm(record.ySize);
	        const { isVerticalSide } = AltiumShapeConverters$1.getHarnessSideGeometry(record.side);
	        const sideSpan = isVerticalSide ? height : width;
	        const bodyDepth = isVerticalSide ? width : height;
	        return Math.min(sideSpan / 2, AltiumShapeConverters$1.HarnessBraceMaximumDepth, bodyDepth);
	    }
	    static getHarnessSideGeometry(side) {
	        switch (side) {
	            case AltiumEnums_1.SheetSymbolSide.Right:
	                return { isVerticalSide: true, outerEdgeRatio: [1, 0], outward: [1, 0], tangent: [0, 1] };
	            case AltiumEnums_1.SheetSymbolSide.Top:
	                return { isVerticalSide: false, outerEdgeRatio: [0, 0], outward: [0, -1], tangent: [1, 0] };
	            case AltiumEnums_1.SheetSymbolSide.Bottom:
	                return { isVerticalSide: false, outerEdgeRatio: [0, 1], outward: [0, 1], tangent: [1, 0] };
	            case AltiumEnums_1.SheetSymbolSide.Left:
	            default:
	                return { isVerticalSide: true, outerEdgeRatio: [0, 0], outward: [-1, 0], tangent: [0, 1] };
	        }
	    }
	    static sampleHarnessBraceArm(start, end, side, braceDepth) {
	        const { outward, tangent } = AltiumShapeConverters$1.getHarnessSideGeometry(side);
	        const tangentSpan = (end[0] - start[0]) * tangent[0] + (end[1] - start[1]) * tangent[1];
	        const points = [];
	        for (let i = 0; i <= AltiumShapeConverters$1.HarnessBraceArcSampleCount; i++) {
	            const angle = (i / AltiumShapeConverters$1.HarnessBraceArcSampleCount) * (Math.PI / 2);
	            points.push(AltiumShapeConverters$1.calculateHarnessBracePoint(start, outward, tangent, (braceDepth / 2) * Math.sin(angle), (tangentSpan / 2) * (1 - Math.cos(angle))));
	        }
	        for (let i = 1; i <= AltiumShapeConverters$1.HarnessBraceArcSampleCount; i++) {
	            const angle = (i / AltiumShapeConverters$1.HarnessBraceArcSampleCount) * (Math.PI / 2);
	            points.push(AltiumShapeConverters$1.calculateHarnessBracePoint(start, outward, tangent, braceDepth / 2 + (braceDepth / 2) * (1 - Math.cos(angle)), tangentSpan / 2 + (tangentSpan / 2) * Math.sin(angle)));
	        }
	        return points;
	    }
	    static calculateHarnessBracePoint(start, outward, tangent, normalOffset, tangentOffset) {
	        return [
	            start[0] + outward[0] * normalOffset + tangent[0] * tangentOffset,
	            start[1] + outward[1] * normalOffset + tangent[1] * tangentOffset,
	        ];
	    }
	    static resolveHarnessFont(sheet, fontId, projectFontId, options) {
	        if (fontId === 0) {
	            return { fontInfo: sheet.getFont(0), projectFontId: options.defaultProjectFontId };
	        }
	        if (!sheet.getFonts().has(fontId)) {
	            if (!options.reportedMissingFontIds.has(fontId)) {
	                AltiumShapeConverters$1.Logger.warn('ResolveHarnessFont_FontNotFound', { fontId });
	                options.reportedMissingFontIds.add(fontId);
	            }
	            return { fontInfo: sheet.getFont(0), projectFontId: options.defaultProjectFontId };
	        }
	        return { fontInfo: sheet.getFont(fontId), projectFontId };
	    }
	    static convertBusEntry(record) {
	        return {
	            type: 'track',
	            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	            end: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y)],
	            width: 0.254,
	        };
	    }
	    static convertPolygon(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        return {
	            type: 'polygon',
	            points: record.points.map((p) => [CoordinateConverter_1.CoordinateConverter.milsToMm(p.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(p.y)]),
	            filled: record.isSolid,
	            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertArc(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        let startAngle = -record.endAngle;
	        let endAngle = -record.startAngle;
	        startAngle = sigma_engine_shared_1.Utils.Transform.clampRotation(startAngle);
	        endAngle = sigma_engine_shared_1.Utils.Transform.clampRotation(endAngle);
	        if (startAngle > endAngle) {
	            endAngle += 360;
	        }
	        if (Math.abs(startAngle - endAngle) < 0.001) {
	            return {
	                type: 'circle',
	                center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
	                radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
	                filled: record.isSolid,
	                width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	            };
	        }
	        return {
	            type: 'arc',
	            center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
	            radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
	            endAngle,
	            startAngle,
	            lineWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertBezier(record, invertY = false) {
	        if (record.points.length === 0) {
	            throw new Error('Bezier curve must have at least 1 point');
	        }
	        const ySign = invertY ? -1 : 1;
	        const toMm = (point) => [
	            CoordinateConverter_1.CoordinateConverter.milsToMm(point.x),
	            ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(point.y),
	        ];
	        const lerp = (a, b, t) => ({
	            x: a.x + (b.x - a.x) * t,
	            y: a.y + (b.y - a.y) * t,
	        });
	        const [start, ...rest] = record.points;
	        const end = record.points.length >= 4 ? record.points[3] : (rest[rest.length - 1] ?? start);
	        let cpa = start;
	        let cpb = end;
	        if (record.points.length === 3) {
	            cpa = lerp(start, record.points[1], 2 / 3);
	            cpb = lerp(end, record.points[1], 2 / 3);
	        }
	        else if (record.points.length >= 4) {
	            cpa = record.points[1];
	            cpb = record.points[2];
	        }
	        return {
	            type: 'curve',
	            start: toMm(start),
	            cpa: toMm(cpa),
	            cpb: toMm(cpb),
	            end: toMm(end),
	            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertEllipse(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        if (record.radius === record.secondaryRadius) {
	            return {
	                type: 'circle',
	                center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
	                radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
	                filled: record.isSolid,
	                width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	            };
	        }
	        return {
	            type: 'circle',
	            center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
	            radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
	            filled: record.isSolid,
	            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertEllipticalArc(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        if (record.radius === record.secondaryRadius) {
	            return {
	                type: 'arc',
	                center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
	                radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
	                startAngle: record.startAngle,
	                endAngle: record.endAngle,
	                lineWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	            };
	        }
	        return {
	            type: 'arc',
	            center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
	            radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
	            startAngle: record.startAngle,
	            endAngle: record.endAngle,
	            lineWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	        };
	    }
	    static convertRoundRectangle(record, invertY = false) {
	        const ySign = invertY ? -1 : 1;
	        return {
	            type: 'rect',
	            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	            end: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y)],
	            strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	            filled: record.isSolid,
	        };
	    }
	    static convertPin(record, componentOrientation = AltiumEnums_1.Orientation.Rotate0) {
	        const pinModel = {
	            type: 'pin',
	            ref: record.designator,
	            name: record.name,
	            number: record.designator,
	            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	            end: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	            layer: (0, layers_1.getAltiumSchLayerByRecordType)(record.recordType).id,
	            width: 0.254,
	            ...(record.hidden ? { hidden: true } : {}),
	        };
	        const pinLengthMm = -CoordinateConverter_1.CoordinateConverter.milsToMm(record.pinLength) * 10;
	        switch (record.orientation) {
	            case AltiumEnums_1.Orientation.Rotate0:
	                pinModel.start[0] += -pinLengthMm;
	                break;
	            case AltiumEnums_1.Orientation.Rotate90:
	                pinModel.start[1] += pinLengthMm;
	                break;
	            case AltiumEnums_1.Orientation.Rotate180:
	                pinModel.start[0] -= -pinLengthMm;
	                break;
	            case AltiumEnums_1.Orientation.Rotate270:
	                pinModel.start[1] -= pinLengthMm;
	                break;
	        }
	        const horizontalAngle = sigma_engine_shared_1.Utils.Transform.clampRotation((componentOrientation - 1) * 90);
	        const verticalAngle = sigma_engine_shared_1.Utils.Transform.clampRotation(componentOrientation * 90);
	        let designatorOffset;
	        let designatorBodyInset;
	        let designatorAnchor = pinModel.end;
	        let designatorJustify;
	        let designatorAngle;
	        let nameOffset;
	        let nameJustify;
	        let nameAngle;
	        switch (record.orientation) {
	            case AltiumEnums_1.Orientation.Rotate0:
	                designatorOffset = [0, -0.5];
	                designatorBodyInset = [AltiumShapeConverters$1.PinDesignatorBodyInset, 0];
	                designatorAngle = horizontalAngle;
	                designatorJustify = [1, 1];
	                nameOffset = [-1, 0];
	                nameAngle = horizontalAngle;
	                nameJustify = [1, 0];
	                break;
	            case AltiumEnums_1.Orientation.Rotate90:
	                designatorOffset = [-0.5, 0];
	                designatorBodyInset = [0, -AltiumShapeConverters$1.PinDesignatorBodyInset];
	                designatorAngle = verticalAngle;
	                designatorJustify = [1, 1];
	                nameOffset = [0, -1];
	                nameAngle = verticalAngle;
	                nameJustify = [0, 1];
	                break;
	            case AltiumEnums_1.Orientation.Rotate180:
	                designatorOffset = [0, -0.5];
	                designatorBodyInset = [-AltiumShapeConverters$1.PinDesignatorBodyInset, 0];
	                designatorAngle = horizontalAngle;
	                designatorJustify = [-1, 1];
	                nameOffset = [1, 0];
	                nameAngle = horizontalAngle;
	                nameJustify = [-1, 0];
	                break;
	            case AltiumEnums_1.Orientation.Rotate270:
	                designatorOffset = [0.5, 0];
	                designatorBodyInset = [0, AltiumShapeConverters$1.PinDesignatorBodyInset];
	                designatorAngle = verticalAngle;
	                designatorJustify = [-1, -1];
	                nameOffset = [0, 1];
	                nameAngle = verticalAngle;
	                nameJustify = [0, -1];
	                break;
	        }
	        if (record.name !== record.designator) {
	            designatorAnchor = [(pinModel.start[0] + pinModel.end[0]) / 2, (pinModel.start[1] + pinModel.end[1]) / 2];
	            designatorBodyInset = [0, 0];
	            designatorJustify = [0, designatorJustify[1]];
	        }
	        if (record.showDesignator) {
	            pinModel.drawings ??= [];
	            pinModel.drawings.push({
	                layers: [pinModel.layer],
	                drawing: {
	                    type: 'text',
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(record.designator),
	                    pos: [
	                        designatorAnchor[0] + designatorOffset[0] + designatorBodyInset[0],
	                        designatorAnchor[1] + designatorOffset[1] + designatorBodyInset[1],
	                    ],
	                    angle: designatorAngle,
	                    justify: designatorJustify,
	                    size: 1,
	                    thickness: 1,
	                },
	            });
	        }
	        if (record.showPinName) {
	            pinModel.drawings ??= [];
	            pinModel.drawings.push({
	                layers: [pinModel.layer],
	                drawing: {
	                    type: 'text',
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(record.name),
	                    pos: [pinModel.end[0] + nameOffset[0], pinModel.end[1] + nameOffset[1]],
	                    angle: nameAngle,
	                    justify: nameJustify,
	                    size: 1,
	                    thickness: 1,
	                },
	            });
	        }
	        return pinModel;
	    }
	    static snapPositionToGrid(position, snapGridSizeMils) {
	        const literalMils = [
	            CoordinateConverter_1.CoordinateConverter.mmToMils(position[0]),
	            CoordinateConverter_1.CoordinateConverter.mmToMils(position[1]),
	        ];
	        const snapAxis = (value) => Math.round(value / snapGridSizeMils) * snapGridSizeMils;
	        const snappedMils = [snapAxis(literalMils[0]), snapAxis(literalMils[1])];
	        const identicalToleranceMils = 1e-6;
	        if (Math.abs(snappedMils[0] - literalMils[0]) < identicalToleranceMils &&
	            Math.abs(snappedMils[1] - literalMils[1]) < identicalToleranceMils) {
	            return undefined;
	        }
	        return [CoordinateConverter_1.CoordinateConverter.milsToMm(snappedMils[0]), CoordinateConverter_1.CoordinateConverter.milsToMm(snappedMils[1])];
	    }
	    static convertPowerPort(record) {
	        const shapes = [];
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(10);
	        const thinLineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(5);
	        let labelYOffsetMils = -260;
	        const m = (mils) => CoordinateConverter_1.CoordinateConverter.milsToMm(mils);
	        const addSegment = (x1, y1, x2, y2, width = lineWidth) => {
	            shapes.push({
	                type: 'segment',
	                start: [m(x1), -m(y1)],
	                end: [m(x2), -m(y2)],
	                width,
	            });
	        };
	        const addPolygon = (points, filled = false, width = lineWidth) => {
	            shapes.push({
	                type: 'polygon',
	                points: points.map(([x, y]) => [m(x), -m(y)]),
	                filled,
	                width,
	            });
	        };
	        const addCircle = (cx, cy, radius, filled = false, width = thinLineWidth) => {
	            shapes.push({
	                type: 'circle',
	                center: [m(cx), -m(cy)],
	                radius: m(radius),
	                filled,
	                width,
	            });
	        };
	        const addBezier = (startX, startY, cp1X, cp1Y, cp2X, cp2Y, endX, endY, width = thinLineWidth) => {
	            shapes.push({
	                type: 'curve',
	                start: [m(startX), -m(startY)],
	                cpa: [m(cp1X), -m(cp1Y)],
	                cpb: [m(cp2X), -m(cp2Y)],
	                end: [m(endX), -m(endY)],
	                width,
	            });
	        };
	        if (record.isCrossSheetConnector) {
	            const arrowWidth = 50;
	            const arrowSpacing = 5;
	            const arrowHeight = 45;
	            const offset = 2.54 * 7;
	            const style = record.style;
	            switch (style) {
	                case AltiumEnums_1.OffSheetConnectorStyle.LeftArrow:
	                    addPolygon([
	                        [offset + arrowWidth, -arrowHeight],
	                        [offset + 0, 0],
	                        [offset + arrowWidth, arrowHeight],
	                    ]);
	                    addPolygon([
	                        [offset + arrowWidth * 2 + arrowSpacing, -arrowHeight],
	                        [offset + 0 + arrowSpacing + arrowWidth, 0],
	                        [offset + arrowWidth * 2 + arrowSpacing, arrowHeight],
	                    ]);
	                    labelYOffsetMils = -(offset * 1.5 + arrowWidth * 2 + arrowSpacing * 2);
	                    break;
	                case AltiumEnums_1.OffSheetConnectorStyle.RightArrow:
	                    addSegment(0, 0, arrowWidth + offset * 1.5 - 2 * 2.54, 0);
	                    addPolygon([
	                        [-offset * 1.5 + arrowWidth, -arrowHeight],
	                        [-offset * 1.5 + arrowWidth + arrowWidth, 0],
	                        [-offset * 1.5 + arrowWidth, arrowHeight],
	                    ]);
	                    addPolygon([
	                        [-offset * 1.5 + arrowWidth + arrowSpacing + arrowWidth, -arrowHeight],
	                        [-offset * 1.5 + arrowWidth + arrowSpacing + arrowWidth + arrowWidth, 0],
	                        [-offset * 1.5 + arrowWidth + arrowSpacing + arrowWidth, arrowHeight],
	                    ]);
	                    labelYOffsetMils = -(offset * 2 + arrowWidth * 2 + arrowSpacing * 2);
	                    break;
	                default:
	                    throw new Error('Unsupported off-sheet connector style');
	            }
	        }
	        else {
	            switch (record.style) {
	                case AltiumEnums_1.PowerPortStyle.Circle:
	                    addSegment(0, 0, 0, 50);
	                    addCircle(0, 75, 25);
	                    labelYOffsetMils = -180;
	                    break;
	                case AltiumEnums_1.PowerPortStyle.Arrow:
	                    addSegment(0, 0, 0, 50);
	                    addPolygon([
	                        [-25, 50],
	                        [25, 50],
	                        [0, 100],
	                        [-25, 50],
	                    ]);
	                    labelYOffsetMils = -180;
	                    break;
	                case AltiumEnums_1.PowerPortStyle.Wave:
	                    addSegment(0, 0, 0, 72);
	                    addBezier(30, 50, 30, 87, -30, 63, -30, 100);
	                    labelYOffsetMils = -180;
	                    break;
	                case AltiumEnums_1.PowerPortStyle.PowerGround:
	                    addSegment(0, 0, 0, 100);
	                    addSegment(-100, 100, 100, 100);
	                    addSegment(-70, 130, 70, 130);
	                    addSegment(-40, 160, 40, 160);
	                    addSegment(-10, 190, 10, 190);
	                    break;
	                case AltiumEnums_1.PowerPortStyle.SignalGround:
	                    addSegment(0, 0, 0, 100);
	                    addPolygon([
	                        [-100, 100],
	                        [100, 100],
	                        [0, 200],
	                        [-100, 100],
	                    ]);
	                    break;
	                case AltiumEnums_1.PowerPortStyle.Earth:
	                    addSegment(0, 0, 0, 100);
	                    addPolygon([
	                        [-150, 200],
	                        [-100, 100],
	                        [100, 100],
	                        [50, 200],
	                    ]);
	                    addSegment(0, 100, -50, 200);
	                    break;
	                case AltiumEnums_1.PowerPortStyle.GostArrow:
	                    addSegment(0, 0, 0, 100);
	                    addPolygon([
	                        [-25, 50],
	                        [0, 100],
	                        [25, 50],
	                    ]);
	                    labelYOffsetMils = -180;
	                    break;
	                case AltiumEnums_1.PowerPortStyle.GostPowerGround:
	                    addSegment(0, 0, 0, 160);
	                    addSegment(-100, 160, 100, 160);
	                    addSegment(-60, 200, 60, 200);
	                    addSegment(-20, 240, 20, 240);
	                    labelYOffsetMils = -310;
	                    break;
	                case AltiumEnums_1.PowerPortStyle.GostEarth:
	                    addSegment(0, 0, 0, 160);
	                    addSegment(-100, 160, 100, 160);
	                    addSegment(-60, 200, 60, 200);
	                    addSegment(-20, 240, 20, 240);
	                    addCircle(0, 160, 120);
	                    labelYOffsetMils = -340;
	                    break;
	                case AltiumEnums_1.PowerPortStyle.GostBar:
	                    addSegment(0, 0, 0, 200);
	                    addSegment(-100, 200, 100, 200);
	                    break;
	                case AltiumEnums_1.PowerPortStyle.Bar:
	                default:
	                    addSegment(0, 0, 0, 100);
	                    addSegment(-50, 100, 50, 100);
	                    labelYOffsetMils = -180;
	                    break;
	            }
	        }
	        return { graphics: shapes, labelYOffset: CoordinateConverter_1.CoordinateConverter.milsToMm(labelYOffsetMils) };
	    }
	    static convertWire(record) {
	        const tracks = [];
	        for (let i = 0; i < record.points.length - 1; i++) {
	            tracks.push({
	                type: 'track',
	                start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i].x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i].y)],
	                end: [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i + 1].x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i + 1].y),
	                ],
	                width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters$1.LineWidthFactor,
	            });
	        }
	        return tracks;
	    }
	    static convertJunction(record) {
	        return {
	            type: 'junction',
	            position: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	        };
	    }
	    static convertNetLabel(record, sheet, projectFontId) {
	        const text = {
	            type: 'text',
	            pos: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	            value: record.text,
	            angle: record.orientation * -90,
	            thickness: 1,
	            justify: CoordinateConverter_1.CoordinateConverter.convertJustification(record.justification),
	            ...sheet.getFont(record.fontId).textOpts,
	            font: projectFontId,
	        };
	        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
	    }
	    static convertLabel(record, sheet, projectFontId) {
	        const text = {
	            type: 'text',
	            pos: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), -CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
	            value: record.text,
	            angle: record.orientation * -90,
	            thickness: 1,
	            justify: CoordinateConverter_1.CoordinateConverter.convertJustification(record.justification),
	            ...sheet.getFont(record.fontId).textOpts,
	            font: projectFontId,
	        };
	        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
	    }
	    static convertTextFrame(record, sheet, projectFontId, invertY = false) {
	        const locX = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
	        const locY = (invertY ? -1 : 1) * CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
	        const cornerX = CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x);
	        const cornerY = (invertY ? -1 : 1) * CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y);
	        const width = Math.abs(cornerX - locX);
	        const height = Math.abs(cornerY - locY);
	        const marginMm = CoordinateConverter_1.CoordinateConverter.milsToMm(record.textMargin);
	        let textAlign;
	        let justify;
	        switch (record.alignment) {
	            case AltiumEnums_1.TextFrameAlignment.Center:
	                textAlign = 'center';
	                justify = [0, -1];
	                break;
	            case AltiumEnums_1.TextFrameAlignment.Right:
	                textAlign = 'right';
	                justify = [1, -1];
	                break;
	            case AltiumEnums_1.TextFrameAlignment.Left:
	            default:
	                textAlign = 'left';
	                justify = [-1, -1];
	                break;
	        }
	        const text = PropertyParser_1.PropertyParser.parseAltiumTextTransforms({
	            type: 'text',
	            pos: [0, 0],
	            value: record.text,
	            angle: 0,
	            thickness: 1,
	            justify,
	            textAlign,
	            bounds: [width, height],
	            padding: [marginMm, marginMm],
	            ...sheet.getFont(record.fontId).textOpts,
	            font: projectFontId,
	            wordWrap: record.isWordWrapped,
	            clipToBounds: record.clipToRect,
	            contrastWithLayer: record.isSolid,
	        });
	        const drawings = [];
	        if (record.showBorder || record.isSolid) {
	            const rect = {
	                type: 'rect',
	                start: [0, 0],
	                end: [width, height],
	                strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.borderWidth) * AltiumShapeConverters$1.LineWidthFactor,
	                filled: record.isSolid,
	            };
	            drawings.push(rect);
	        }
	        drawings.push(text);
	        const minX = Math.min(locX, cornerX);
	        const minY = Math.min(locY, cornerY);
	        return {
	            type: 'group',
	            pos: [minX, minY],
	            drawings,
	        };
	    }
	    static convertSheetSymbol(record) {
	        const x = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
	        const y = -CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
	        const width = CoordinateConverter_1.CoordinateConverter.milsToMm(record.xSize);
	        const height = CoordinateConverter_1.CoordinateConverter.milsToMm(record.ySize);
	        return {
	            type: 'rect',
	            start: [x, -y],
	            end: [x + width, -(y + height)],
	            strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(10),
	            filled: record.isSolid,
	        };
	    }
	    static generateSheetEntryIconDrawings(entry, relativePosX, relativePosY) {
	        const drawings = [];
	        const iconWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(150);
	        const iconHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(20 * 4);
	        const arrowDepth = CoordinateConverter_1.CoordinateConverter.milsToMm(5 * 8);
	        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(1 * 3);
	        if (entry.ioType === AltiumEnums_1.PortIOType.Unspecified) {
	            return { drawings, width: 0, height: 0 };
	        }
	        let points = [];
	        if (entry.ioType === AltiumEnums_1.PortIOType.Input) {
	            switch (entry.side) {
	                case AltiumEnums_1.SheetSymbolSide.Left:
	                    points = [
	                        [0, -iconHeight / 2],
	                        [iconWidth - arrowDepth, -iconHeight / 2],
	                        [iconWidth, 0],
	                        [iconWidth - arrowDepth, iconHeight / 2],
	                        [0, iconHeight / 2],
	                        [0, -iconHeight / 2],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Right:
	                    points = [
	                        [0, -iconHeight / 2],
	                        [-iconWidth + arrowDepth, -iconHeight / 2],
	                        [-iconWidth, 0],
	                        [-iconWidth + arrowDepth, iconHeight / 2],
	                        [0, iconHeight / 2],
	                        [0, -iconHeight / 2],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Top:
	                    points = [
	                        [-iconHeight / 2, 0],
	                        [-iconHeight / 2, iconWidth - arrowDepth],
	                        [0, iconWidth],
	                        [iconHeight / 2, iconWidth - arrowDepth],
	                        [iconHeight / 2, 0],
	                        [-iconHeight / 2, 0],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Bottom:
	                    points = [
	                        [-iconHeight / 2, 0],
	                        [-iconHeight / 2, -iconWidth + arrowDepth],
	                        [0, -iconWidth],
	                        [iconHeight / 2, -iconWidth + arrowDepth],
	                        [iconHeight / 2, 0],
	                        [-iconHeight / 2, 0],
	                    ];
	                    break;
	            }
	        }
	        else if (entry.ioType === AltiumEnums_1.PortIOType.Output) {
	            switch (entry.side) {
	                case AltiumEnums_1.SheetSymbolSide.Left:
	                    points = [
	                        [iconWidth, -iconHeight / 2],
	                        [arrowDepth, -iconHeight / 2],
	                        [0, 0],
	                        [arrowDepth, iconHeight / 2],
	                        [iconWidth, iconHeight / 2],
	                        [iconWidth, -iconHeight / 2],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Right:
	                    points = [
	                        [-iconWidth, -iconHeight / 2],
	                        [-arrowDepth, -iconHeight / 2],
	                        [0, 0],
	                        [-arrowDepth, iconHeight / 2],
	                        [-iconWidth, iconHeight / 2],
	                        [-iconWidth, -iconHeight / 2],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Top:
	                    points = [
	                        [-iconHeight / 2, iconWidth],
	                        [-iconHeight / 2, arrowDepth],
	                        [0, 0],
	                        [iconHeight / 2, arrowDepth],
	                        [iconHeight / 2, iconWidth],
	                        [-iconHeight / 2, iconWidth],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Bottom:
	                    points = [
	                        [-iconHeight / 2, -iconWidth],
	                        [-iconHeight / 2, -arrowDepth],
	                        [0, 0],
	                        [iconHeight / 2, -arrowDepth],
	                        [iconHeight / 2, -iconWidth],
	                        [-iconHeight / 2, -iconWidth],
	                    ];
	                    break;
	            }
	        }
	        else if (entry.ioType === AltiumEnums_1.PortIOType.Bidirectional) {
	            switch (entry.side) {
	                case AltiumEnums_1.SheetSymbolSide.Left:
	                    points = [
	                        [arrowDepth, -iconHeight / 2],
	                        [iconWidth - arrowDepth, -iconHeight / 2],
	                        [iconWidth, 0],
	                        [iconWidth - arrowDepth, iconHeight / 2],
	                        [arrowDepth, iconHeight / 2],
	                        [0, 0],
	                        [arrowDepth, -iconHeight / 2],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Right:
	                    points = [
	                        [-arrowDepth, -iconHeight / 2],
	                        [-iconWidth + arrowDepth, -iconHeight / 2],
	                        [-iconWidth, 0],
	                        [-iconWidth + arrowDepth, iconHeight / 2],
	                        [-arrowDepth, iconHeight / 2],
	                        [0, 0],
	                        [-arrowDepth, -iconHeight / 2],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Top:
	                    points = [
	                        [-iconHeight / 2, arrowDepth],
	                        [-iconHeight / 2, iconWidth - arrowDepth],
	                        [0, iconWidth],
	                        [iconHeight / 2, iconWidth - arrowDepth],
	                        [iconHeight / 2, arrowDepth],
	                        [0, 0],
	                        [-iconHeight / 2, arrowDepth],
	                    ];
	                    break;
	                case AltiumEnums_1.SheetSymbolSide.Bottom:
	                    points = [
	                        [-iconHeight / 2, -arrowDepth],
	                        [-iconHeight / 2, -iconWidth + arrowDepth],
	                        [0, -iconWidth],
	                        [iconHeight / 2, -iconWidth + arrowDepth],
	                        [iconHeight / 2, -arrowDepth],
	                        [0, 0],
	                        [-iconHeight / 2, -arrowDepth],
	                    ];
	                    break;
	            }
	        }
	        if (points.length > 0) {
	            const polygon = {
	                type: 'polygon',
	                points,
	                filled: true,
	                width: lineWidth,
	            };
	            const translatedPolygon = sigma_engine_shared_1.Utils.Transform.translateDrawing(polygon, [relativePosX, relativePosY]);
	            drawings.push(translatedPolygon);
	        }
	        return { drawings, width: iconWidth, height: iconHeight };
	    }
	    static calculateSheetEntryPosition(sheetSymbol, entry) {
	        const sheetX = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.location.x);
	        const sheetY = -CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.location.y);
	        const sheetWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.xSize);
	        const sheetHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.ySize);
	        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.milsToMm(entry.distanceFromTop) * AltiumShapeConverters$1.LineWidthFactor;
	        switch (entry.side) {
	            case AltiumEnums_1.SheetSymbolSide.Left:
	                return [sheetX, -sheetY + distanceFromTop];
	            case AltiumEnums_1.SheetSymbolSide.Right:
	                return [sheetX + sheetWidth, -sheetY + distanceFromTop];
	            case AltiumEnums_1.SheetSymbolSide.Top:
	                return [sheetX + distanceFromTop, -sheetY];
	            case AltiumEnums_1.SheetSymbolSide.Bottom:
	                return [sheetX + distanceFromTop, -sheetY + sheetHeight];
	            default:
	                return [sheetX, -sheetY + distanceFromTop];
	        }
	    }
	    static convertSheetEntryLabel(entry, sheet, position, offset, projectFontId) {
	        const labelSpacing = CoordinateConverter_1.CoordinateConverter.milsToMm(40);
	        const adjustedPos = [...position];
	        let justify = [0, 0];
	        switch (entry.side) {
	            case AltiumEnums_1.SheetSymbolSide.Left:
	                adjustedPos[0] += offset[0] + labelSpacing;
	                justify = [-1, 0];
	                break;
	            case AltiumEnums_1.SheetSymbolSide.Right:
	                adjustedPos[0] -= offset[0] + labelSpacing;
	                justify = [1, 0];
	                break;
	            case AltiumEnums_1.SheetSymbolSide.Top:
	                adjustedPos[1] += offset[1] + labelSpacing;
	                justify = [0, -1];
	                break;
	            case AltiumEnums_1.SheetSymbolSide.Bottom:
	                adjustedPos[1] -= offset[1] + labelSpacing;
	                justify = [0, 1];
	                break;
	        }
	        const text = {
	            type: 'text',
	            pos: adjustedPos,
	            value: entry.name,
	            angle: 0,
	            thickness: 1,
	            justify,
	            ...sheet.getFont(entry.textFontId).textOpts,
	            font: projectFontId,
	        };
	        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
	    }
	    static convertPort(port, sheet, projectFontId) {
	        const pos = [
	            CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.x),
	            CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.y),
	        ];
	        const isVertical = port.style === AltiumEnums_1.PortStyle.NoneVertical ||
	            port.style === AltiumEnums_1.PortStyle.Top ||
	            port.style === AltiumEnums_1.PortStyle.Bottom ||
	            port.style === AltiumEnums_1.PortStyle.TopBottom;
	        const portWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(port.width);
	        const portHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(port.height);
	        const arrowDepth = portHeight * 0.55;
	        const { drawings: shapeDrawings } = this.createPortShape(port, portWidth, portHeight, arrowDepth, isVertical);
	        const textLabel = this.createPortText(port, sheet, arrowDepth, isVertical, projectFontId);
	        const drawings = [...shapeDrawings, textLabel];
	        return {
	            type: 'group',
	            pos,
	            angle: isVertical ? 90 : 0,
	            drawings,
	        };
	    }
	    static createPortShape(port, width, height, arrowDepth, _isVertical) {
	        const drawings = [];
	        const iconWidth = width;
	        const iconHeight = height;
	        const lineWidth = 0.254;
	        let points = [];
	        if (port.ioType === AltiumEnums_1.PortIOType.Output) {
	            points = [
	                [0, -iconHeight / 2],
	                [iconWidth - arrowDepth, -iconHeight / 2],
	                [iconWidth, 0],
	                [iconWidth - arrowDepth, iconHeight / 2],
	                [0, iconHeight / 2],
	                [0, -iconHeight / 2],
	            ];
	        }
	        else if (port.ioType === AltiumEnums_1.PortIOType.Input) {
	            points = [
	                [iconWidth, -iconHeight / 2],
	                [arrowDepth, -iconHeight / 2],
	                [0, 0],
	                [arrowDepth, iconHeight / 2],
	                [iconWidth, iconHeight / 2],
	                [iconWidth, -iconHeight / 2],
	            ];
	        }
	        else if (port.ioType === AltiumEnums_1.PortIOType.Bidirectional) {
	            points = [
	                [arrowDepth, -iconHeight / 2],
	                [iconWidth - arrowDepth, -iconHeight / 2],
	                [iconWidth, 0],
	                [iconWidth - arrowDepth, iconHeight / 2],
	                [arrowDepth, iconHeight / 2],
	                [0, 0],
	                [arrowDepth, -iconHeight / 2],
	            ];
	        }
	        else {
	            points = [
	                [0, -iconHeight / 2],
	                [iconWidth, -iconHeight / 2],
	                [iconWidth, iconHeight / 2],
	                [0, iconHeight / 2],
	                [0, -iconHeight / 2],
	            ];
	        }
	        if (points.length > 0) {
	            const polygon = {
	                type: 'polygon',
	                points,
	                filled: false,
	                width: lineWidth,
	            };
	            drawings.push(polygon);
	        }
	        return { drawings };
	    }
	    static createPortText(port, sheet, arrowWidth, _isVertical, projectFontId) {
	        const labelSpacing = arrowWidth + CoordinateConverter_1.CoordinateConverter.milsToMm(40);
	        const textX = labelSpacing;
	        const textY = 0;
	        const text = {
	            type: 'text',
	            pos: [textX, textY],
	            value: port.name,
	            angle: 0,
	            thickness: 1,
	            justify: [-1, -0.05],
	            ...sheet.getFont(port.fontId).textOpts,
	            font: projectFontId,
	        };
	        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
	    }
	    static convertImage(record, imageRef) {
	        const x1 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
	        const y1 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
	        const x2 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x);
	        const y2 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y);
	        return {
	            type: 'image',
	            pos: [Math.min(x1, x2), Math.min(y1, y2)],
	            width: Math.abs(x2 - x1),
	            height: Math.abs(y2 - y1),
	            imageRef,
	        };
	    }
	}
	AltiumShapeConverters.AltiumShapeConverters = AltiumShapeConverters$1;
	
	return AltiumShapeConverters;
}

var AltiumCombobulatorConfig = {};

var hasRequiredAltiumCombobulatorConfig;

function requireAltiumCombobulatorConfig () {
	if (hasRequiredAltiumCombobulatorConfig) return AltiumCombobulatorConfig;
	hasRequiredAltiumCombobulatorConfig = 1;
	Object.defineProperty(AltiumCombobulatorConfig, "__esModule", { value: true });
	AltiumCombobulatorConfig.ALTIUM_CONNECTION_TOLERANCE = AltiumCombobulatorConfig.ALTIUM_POINT_ROUNDING_PRECISION = void 0;
	AltiumCombobulatorConfig.ALTIUM_POINT_ROUNDING_PRECISION = 0.01;
	AltiumCombobulatorConfig.ALTIUM_CONNECTION_TOLERANCE = 0.03;
	
	return AltiumCombobulatorConfig;
}

var browser = {};

var hasRequiredBrowser;

function requireBrowser () {
	if (hasRequiredBrowser) return browser;
	hasRequiredBrowser = 1;
	// DEFLATE is a complex format; to read this code, you should probably check the RFC first:
	// https://tools.ietf.org/html/rfc1951
	// You may also wish to take a look at the guide I made about this program:
	// https://gist.github.com/101arrowz/253f31eb5abc3d9275ab943003ffecad
	browser.deflate = deflate;
	browser.deflateSync = deflateSync;
	browser.inflate = inflate;
	browser.inflateSync = inflateSync;
	browser.gzip = gzip;
	browser.compress = gzip;
	browser.gzipSync = gzipSync;
	browser.compressSync = gzipSync;
	browser.gunzip = gunzip;
	browser.gunzipSync = gunzipSync;
	browser.zlib = zlib;
	browser.zlibSync = zlibSync;
	browser.unzlib = unzlib;
	browser.unzlibSync = unzlibSync;
	browser.gzip = gzip;
	browser.compress = gzip;
	browser.decompress = decompress;
	browser.decompressSync = decompressSync;
	browser.strToU8 = strToU8;
	browser.strFromU8 = strFromU8;
	browser.zip = zip;
	browser.zipSync = zipSync;
	browser.unzip = unzip;
	browser.unzipSync = unzipSync;
	// Some of the following code is similar to that of UZIP.js:
	// https://github.com/photopea/UZIP.js
	// However, the vast majority of the codebase has diverged from UZIP.js to increase performance and reduce bundle size.
	// Sometimes 0 will appear where -1 would be more appropriate. This is because using a uint
	// is better for memory in most engines (I *think*).
	var ch2 = {};
	var node_worker_1 = {};
	node_worker_1["default"] = (function (c, id, msg, transfer, cb) {
	    var w = new Worker(ch2[id] || (ch2[id] = URL.createObjectURL(new Blob([
	        c + ';addEventListener("error",function(e){e=e.error;postMessage({$e$:[e.message,e.code,e.stack]})})'
	    ], { type: 'text/javascript' }))));
	    w.onmessage = function (e) {
	        var d = e.data, ed = d.$e$;
	        if (ed) {
	            var err = new Error(ed[0]);
	            err['code'] = ed[1];
	            err.stack = ed[2];
	            cb(err, null);
	        }
	        else
	            cb(null, d);
	    };
	    w.postMessage(msg, transfer);
	    return w;
	});

	// aliases for shorter compressed code (most minifers don't do this)
	var u8 = Uint8Array, u16 = Uint16Array, i32 = Int32Array;
	// fixed length extra bits
	var fleb = new u8([0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0, /* unused */ 0, 0, /* impossible */ 0]);
	// fixed distance extra bits
	var fdeb = new u8([0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13, /* unused */ 0, 0]);
	// code length index map
	var clim = new u8([16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]);
	// get base, reverse index map from extra bits
	var freb = function (eb, start) {
	    var b = new u16(31);
	    for (var i = 0; i < 31; ++i) {
	        b[i] = start += 1 << eb[i - 1];
	    }
	    // numbers here are at max 18 bits
	    var r = new i32(b[30]);
	    for (var i = 1; i < 30; ++i) {
	        for (var j = b[i]; j < b[i + 1]; ++j) {
	            r[j] = ((j - b[i]) << 5) | i;
	        }
	    }
	    return { b: b, r: r };
	};
	var _a = freb(fleb, 2), fl = _a.b, revfl = _a.r;
	// we can ignore the fact that the other numbers are wrong; they never happen anyway
	fl[28] = 258, revfl[258] = 28;
	var _b = freb(fdeb, 0), fd = _b.b, revfd = _b.r;
	// map of value to reverse (assuming 16 bits)
	var rev = new u16(32768);
	for (var i = 0; i < 32768; ++i) {
	    // reverse table algorithm from SO
	    var x = ((i & 0xAAAA) >> 1) | ((i & 0x5555) << 1);
	    x = ((x & 0xCCCC) >> 2) | ((x & 0x3333) << 2);
	    x = ((x & 0xF0F0) >> 4) | ((x & 0x0F0F) << 4);
	    rev[i] = (((x & 0xFF00) >> 8) | ((x & 0x00FF) << 8)) >> 1;
	}
	// create huffman tree from u8 "map": index -> code length for code index
	// mb (max bits) must be at most 15
	// TODO: optimize/split up?
	var hMap = (function (cd, mb, r) {
	    var s = cd.length;
	    // index
	    var i = 0;
	    // u16 "map": index -> # of codes with bit length = index
	    var l = new u16(mb);
	    // length of cd must be 288 (total # of codes)
	    for (; i < s; ++i) {
	        if (cd[i])
	            ++l[cd[i] - 1];
	    }
	    // u16 "map": index -> minimum code for bit length = index
	    var le = new u16(mb);
	    for (i = 1; i < mb; ++i) {
	        le[i] = (le[i - 1] + l[i - 1]) << 1;
	    }
	    var co;
	    if (r) {
	        // u16 "map": index -> number of actual bits, symbol for code
	        co = new u16(1 << mb);
	        // bits to remove for reverser
	        var rvb = 15 - mb;
	        for (i = 0; i < s; ++i) {
	            // ignore 0 lengths
	            if (cd[i]) {
	                // num encoding both symbol and bits read
	                var sv = (i << 4) | cd[i];
	                // free bits
	                var r_1 = mb - cd[i];
	                // start value
	                var v = le[cd[i] - 1]++ << r_1;
	                // m is end value
	                for (var m = v | ((1 << r_1) - 1); v <= m; ++v) {
	                    // every 16 bit value starting with the code yields the same result
	                    co[rev[v] >> rvb] = sv;
	                }
	            }
	        }
	    }
	    else {
	        co = new u16(s);
	        for (i = 0; i < s; ++i) {
	            if (cd[i]) {
	                co[i] = rev[le[cd[i] - 1]++] >> (15 - cd[i]);
	            }
	        }
	    }
	    return co;
	});
	// fixed length tree
	var flt = new u8(288);
	for (var i = 0; i < 144; ++i)
	    flt[i] = 8;
	for (var i = 144; i < 256; ++i)
	    flt[i] = 9;
	for (var i = 256; i < 280; ++i)
	    flt[i] = 7;
	for (var i = 280; i < 288; ++i)
	    flt[i] = 8;
	// fixed distance tree
	var fdt = new u8(32);
	for (var i = 0; i < 32; ++i)
	    fdt[i] = 5;
	// fixed length map
	var flm = /*#__PURE__*/ hMap(flt, 9, 0), flrm = /*#__PURE__*/ hMap(flt, 9, 1);
	// fixed distance map
	var fdm = /*#__PURE__*/ hMap(fdt, 5, 0), fdrm = /*#__PURE__*/ hMap(fdt, 5, 1);
	// find max of array
	var max = function (a) {
	    var m = a[0];
	    for (var i = 1; i < a.length; ++i) {
	        if (a[i] > m)
	            m = a[i];
	    }
	    return m;
	};
	// read d, starting at bit p and mask with m
	var bits = function (d, p, m) {
	    var o = (p / 8) | 0;
	    return ((d[o] | (d[o + 1] << 8)) >> (p & 7)) & m;
	};
	// read d, starting at bit p continuing for at least 16 bits
	var bits16 = function (d, p) {
	    var o = (p / 8) | 0;
	    return ((d[o] | (d[o + 1] << 8) | (d[o + 2] << 16)) >> (p & 7));
	};
	// get end of byte
	var shft = function (p) { return ((p + 7) / 8) | 0; };
	// typed array slice - allows garbage collector to free original reference,
	// while being more compatible than .slice
	var slc = function (v, s, e) {
	    if (s == null || s < 0)
	        s = 0;
	    if (e == null || e > v.length)
	        e = v.length;
	    // can't use .constructor in case user-supplied
	    return new u8(v.subarray(s, e));
	};
	/**
	 * Codes for errors generated within this library
	 */
	browser.FlateErrorCode = {
	    UnexpectedEOF: 0,
	    InvalidBlockType: 1,
	    InvalidLengthLiteral: 2,
	    InvalidDistance: 3,
	    StreamFinished: 4,
	    NoStreamHandler: 5,
	    InvalidHeader: 6,
	    NoCallback: 7,
	    InvalidUTF8: 8,
	    ExtraFieldTooLong: 9,
	    InvalidDate: 10,
	    FilenameTooLong: 11,
	    StreamFinishing: 12,
	    InvalidZipData: 13,
	    UnknownCompressionMethod: 14
	};
	// error codes
	var ec = [
	    'unexpected EOF',
	    'invalid block type',
	    'invalid length/literal',
	    'invalid distance',
	    'stream finished',
	    'no stream handler',
	    , // determined by compression function
	    'no callback',
	    'invalid UTF-8 data',
	    'extra field too long',
	    'date not in range 1980-2099',
	    'filename too long',
	    'stream finishing',
	    'invalid zip data'
	    // determined by unknown compression method
	];
	var err = function (ind, msg, nt) {
	    var e = new Error(msg || ec[ind]);
	    e.code = ind;
	    if (Error.captureStackTrace)
	        Error.captureStackTrace(e, err);
	    if (!nt)
	        throw e;
	    return e;
	};
	// expands raw DEFLATE data
	var inflt = function (dat, st, buf, dict) {
	    // source length       dict length
	    var sl = dat.length, dl = dict ? dict.length : 0;
	    if (!sl || st.f && !st.l)
	        return buf || new u8(0);
	    var noBuf = !buf;
	    // have to estimate size
	    var resize = noBuf || st.i != 2;
	    // no state
	    var noSt = st.i;
	    // Assumes roughly 33% compression ratio average
	    if (noBuf)
	        buf = new u8(sl * 3);
	    // ensure buffer can fit at least l elements
	    var cbuf = function (l) {
	        var bl = buf.length;
	        // need to increase size to fit
	        if (l > bl) {
	            // Double or set to necessary, whichever is greater
	            var nbuf = new u8(Math.max(bl * 2, l));
	            nbuf.set(buf);
	            buf = nbuf;
	        }
	    };
	    //  last chunk         bitpos           bytes
	    var final = st.f || 0, pos = st.p || 0, bt = st.b || 0, lm = st.l, dm = st.d, lbt = st.m, dbt = st.n;
	    // total bits
	    var tbts = sl * 8;
	    do {
	        if (!lm) {
	            // BFINAL - this is only 1 when last chunk is next
	            final = bits(dat, pos, 1);
	            // type: 0 = no compression, 1 = fixed huffman, 2 = dynamic huffman
	            var type = bits(dat, pos + 1, 3);
	            pos += 3;
	            if (!type) {
	                // go to end of byte boundary
	                var s = shft(pos) + 4, l = dat[s - 4] | (dat[s - 3] << 8), t = s + l;
	                if (t > sl) {
	                    if (noSt)
	                        err(0);
	                    break;
	                }
	                // ensure size
	                if (resize)
	                    cbuf(bt + l);
	                // Copy over uncompressed data
	                buf.set(dat.subarray(s, t), bt);
	                // Get new bitpos, update byte count
	                st.b = bt += l, st.p = pos = t * 8, st.f = final;
	                continue;
	            }
	            else if (type == 1)
	                lm = flrm, dm = fdrm, lbt = 9, dbt = 5;
	            else if (type == 2) {
	                //  literal                            lengths
	                var hLit = bits(dat, pos, 31) + 257, hcLen = bits(dat, pos + 10, 15) + 4;
	                var tl = hLit + bits(dat, pos + 5, 31) + 1;
	                pos += 14;
	                // length+distance tree
	                var ldt = new u8(tl);
	                // code length tree
	                var clt = new u8(19);
	                for (var i = 0; i < hcLen; ++i) {
	                    // use index map to get real code
	                    clt[clim[i]] = bits(dat, pos + i * 3, 7);
	                }
	                pos += hcLen * 3;
	                // code lengths bits
	                var clb = max(clt), clbmsk = (1 << clb) - 1;
	                // code lengths map
	                var clm = hMap(clt, clb, 1);
	                for (var i = 0; i < tl;) {
	                    var r = clm[bits(dat, pos, clbmsk)];
	                    // bits read
	                    pos += r & 15;
	                    // symbol
	                    var s = r >> 4;
	                    // code length to copy
	                    if (s < 16) {
	                        ldt[i++] = s;
	                    }
	                    else {
	                        //  copy   count
	                        var c = 0, n = 0;
	                        if (s == 16)
	                            n = 3 + bits(dat, pos, 3), pos += 2, c = ldt[i - 1];
	                        else if (s == 17)
	                            n = 3 + bits(dat, pos, 7), pos += 3;
	                        else if (s == 18)
	                            n = 11 + bits(dat, pos, 127), pos += 7;
	                        while (n--)
	                            ldt[i++] = c;
	                    }
	                }
	                //    length tree                 distance tree
	                var lt = ldt.subarray(0, hLit), dt = ldt.subarray(hLit);
	                // max length bits
	                lbt = max(lt);
	                // max dist bits
	                dbt = max(dt);
	                lm = hMap(lt, lbt, 1);
	                dm = hMap(dt, dbt, 1);
	            }
	            else
	                err(1);
	            if (pos > tbts) {
	                if (noSt)
	                    err(0);
	                break;
	            }
	        }
	        // Make sure the buffer can hold this + the largest possible addition
	        // Maximum chunk size (practically, theoretically infinite) is 2^17
	        if (resize)
	            cbuf(bt + 131072);
	        var lms = (1 << lbt) - 1, dms = (1 << dbt) - 1;
	        var lpos = pos;
	        for (;; lpos = pos) {
	            // bits read, code
	            var c = lm[bits16(dat, pos) & lms], sym = c >> 4;
	            pos += c & 15;
	            if (pos > tbts) {
	                if (noSt)
	                    err(0);
	                break;
	            }
	            if (!c)
	                err(2);
	            if (sym < 256)
	                buf[bt++] = sym;
	            else if (sym == 256) {
	                lpos = pos, lm = null;
	                break;
	            }
	            else {
	                var add = sym - 254;
	                // no extra bits needed if less
	                if (sym > 264) {
	                    // index
	                    var i = sym - 257, b = fleb[i];
	                    add = bits(dat, pos, (1 << b) - 1) + fl[i];
	                    pos += b;
	                }
	                // dist
	                var d = dm[bits16(dat, pos) & dms], dsym = d >> 4;
	                if (!d)
	                    err(3);
	                pos += d & 15;
	                var dt = fd[dsym];
	                if (dsym > 3) {
	                    var b = fdeb[dsym];
	                    dt += bits16(dat, pos) & (1 << b) - 1, pos += b;
	                }
	                if (pos > tbts) {
	                    if (noSt)
	                        err(0);
	                    break;
	                }
	                if (resize)
	                    cbuf(bt + 131072);
	                var end = bt + add;
	                if (bt < dt) {
	                    var shift = dl - dt, dend = Math.min(dt, end);
	                    if (shift + bt < 0)
	                        err(3);
	                    for (; bt < dend; ++bt)
	                        buf[bt] = dict[shift + bt];
	                }
	                for (; bt < end; ++bt)
	                    buf[bt] = buf[bt - dt];
	            }
	        }
	        st.l = lm, st.p = lpos, st.b = bt, st.f = final;
	        if (lm)
	            final = 1, st.m = lbt, st.d = dm, st.n = dbt;
	    } while (!final);
	    // don't reallocate for streams or user buffers
	    return bt != buf.length && noBuf ? slc(buf, 0, bt) : buf.subarray(0, bt);
	};
	// starting at p, write the minimum number of bits that can hold v to d
	var wbits = function (d, p, v) {
	    v <<= p & 7;
	    var o = (p / 8) | 0;
	    d[o] |= v;
	    d[o + 1] |= v >> 8;
	};
	// starting at p, write the minimum number of bits (>8) that can hold v to d
	var wbits16 = function (d, p, v) {
	    v <<= p & 7;
	    var o = (p / 8) | 0;
	    d[o] |= v;
	    d[o + 1] |= v >> 8;
	    d[o + 2] |= v >> 16;
	};
	// creates code lengths from a frequency table
	var hTree = function (d, mb) {
	    // Need extra info to make a tree
	    var t = [];
	    for (var i = 0; i < d.length; ++i) {
	        if (d[i])
	            t.push({ s: i, f: d[i] });
	    }
	    var s = t.length;
	    var t2 = t.slice();
	    if (!s)
	        return { t: et, l: 0 };
	    if (s == 1) {
	        var v = new u8(t[0].s + 1);
	        v[t[0].s] = 1;
	        return { t: v, l: 1 };
	    }
	    t.sort(function (a, b) { return a.f - b.f; });
	    // after i2 reaches last ind, will be stopped
	    // freq must be greater than largest possible number of symbols
	    t.push({ s: -1, f: 25001 });
	    var l = t[0], r = t[1], i0 = 0, i1 = 1, i2 = 2;
	    t[0] = { s: -1, f: l.f + r.f, l: l, r: r };
	    // efficient algorithm from UZIP.js
	    // i0 is lookbehind, i2 is lookahead - after processing two low-freq
	    // symbols that combined have high freq, will start processing i2 (high-freq,
	    // non-composite) symbols instead
	    // see https://reddit.com/r/photopea/comments/ikekht/uzipjs_questions/
	    while (i1 != s - 1) {
	        l = t[t[i0].f < t[i2].f ? i0++ : i2++];
	        r = t[i0 != i1 && t[i0].f < t[i2].f ? i0++ : i2++];
	        t[i1++] = { s: -1, f: l.f + r.f, l: l, r: r };
	    }
	    var maxSym = t2[0].s;
	    for (var i = 1; i < s; ++i) {
	        if (t2[i].s > maxSym)
	            maxSym = t2[i].s;
	    }
	    // code lengths
	    var tr = new u16(maxSym + 1);
	    // max bits in tree
	    var mbt = ln(t[i1 - 1], tr, 0);
	    if (mbt > mb) {
	        // more algorithms from UZIP.js
	        // TODO: find out how this code works (debt)
	        //  ind    debt
	        var i = 0, dt = 0;
	        //    left            cost
	        var lft = mbt - mb, cst = 1 << lft;
	        t2.sort(function (a, b) { return tr[b.s] - tr[a.s] || a.f - b.f; });
	        for (; i < s; ++i) {
	            var i2_1 = t2[i].s;
	            if (tr[i2_1] > mb) {
	                dt += cst - (1 << (mbt - tr[i2_1]));
	                tr[i2_1] = mb;
	            }
	            else
	                break;
	        }
	        dt >>= lft;
	        while (dt > 0) {
	            var i2_2 = t2[i].s;
	            if (tr[i2_2] < mb)
	                dt -= 1 << (mb - tr[i2_2]++ - 1);
	            else
	                ++i;
	        }
	        for (; i >= 0 && dt; --i) {
	            var i2_3 = t2[i].s;
	            if (tr[i2_3] == mb) {
	                --tr[i2_3];
	                ++dt;
	            }
	        }
	        mbt = mb;
	    }
	    return { t: new u8(tr), l: mbt };
	};
	// get the max length and assign length codes
	var ln = function (n, l, d) {
	    return n.s == -1
	        ? Math.max(ln(n.l, l, d + 1), ln(n.r, l, d + 1))
	        : (l[n.s] = d);
	};
	// length codes generation
	var lc = function (c) {
	    var s = c.length;
	    // Note that the semicolon was intentional
	    while (s && !c[--s])
	        ;
	    var cl = new u16(++s);
	    //  ind      num         streak
	    var cli = 0, cln = c[0], cls = 1;
	    var w = function (v) { cl[cli++] = v; };
	    for (var i = 1; i <= s; ++i) {
	        if (c[i] == cln && i != s)
	            ++cls;
	        else {
	            if (!cln && cls > 2) {
	                for (; cls > 138; cls -= 138)
	                    w(32754);
	                if (cls > 2) {
	                    w(cls > 10 ? ((cls - 11) << 5) | 28690 : ((cls - 3) << 5) | 12305);
	                    cls = 0;
	                }
	            }
	            else if (cls > 3) {
	                w(cln), --cls;
	                for (; cls > 6; cls -= 6)
	                    w(8304);
	                if (cls > 2)
	                    w(((cls - 3) << 5) | 8208), cls = 0;
	            }
	            while (cls--)
	                w(cln);
	            cls = 1;
	            cln = c[i];
	        }
	    }
	    return { c: cl.subarray(0, cli), n: s };
	};
	// calculate the length of output from tree, code lengths
	var clen = function (cf, cl) {
	    var l = 0;
	    for (var i = 0; i < cl.length; ++i)
	        l += cf[i] * cl[i];
	    return l;
	};
	// writes a fixed block
	// returns the new bit pos
	var wfblk = function (out, pos, dat) {
	    // no need to write 00 as type: TypedArray defaults to 0
	    var s = dat.length;
	    var o = shft(pos + 2);
	    out[o] = s & 255;
	    out[o + 1] = s >> 8;
	    out[o + 2] = out[o] ^ 255;
	    out[o + 3] = out[o + 1] ^ 255;
	    for (var i = 0; i < s; ++i)
	        out[o + i + 4] = dat[i];
	    return (o + 4 + s) * 8;
	};
	// writes a block
	var wblk = function (dat, out, final, syms, lf, df, eb, li, bs, bl, p) {
	    wbits(out, p++, final);
	    ++lf[256];
	    var _a = hTree(lf, 15), dlt = _a.t, mlb = _a.l;
	    var _b = hTree(df, 15), ddt = _b.t, mdb = _b.l;
	    var _c = lc(dlt), lclt = _c.c, nlc = _c.n;
	    var _d = lc(ddt), lcdt = _d.c, ndc = _d.n;
	    var lcfreq = new u16(19);
	    for (var i = 0; i < lclt.length; ++i)
	        ++lcfreq[lclt[i] & 31];
	    for (var i = 0; i < lcdt.length; ++i)
	        ++lcfreq[lcdt[i] & 31];
	    var _e = hTree(lcfreq, 7), lct = _e.t, mlcb = _e.l;
	    var nlcc = 19;
	    for (; nlcc > 4 && !lct[clim[nlcc - 1]]; --nlcc)
	        ;
	    var flen = (bl + 5) << 3;
	    var ftlen = clen(lf, flt) + clen(df, fdt) + eb;
	    var dtlen = clen(lf, dlt) + clen(df, ddt) + eb + 14 + 3 * nlcc + clen(lcfreq, lct) + 2 * lcfreq[16] + 3 * lcfreq[17] + 7 * lcfreq[18];
	    if (bs >= 0 && flen <= ftlen && flen <= dtlen)
	        return wfblk(out, p, dat.subarray(bs, bs + bl));
	    var lm, ll, dm, dl;
	    wbits(out, p, 1 + (dtlen < ftlen)), p += 2;
	    if (dtlen < ftlen) {
	        lm = hMap(dlt, mlb, 0), ll = dlt, dm = hMap(ddt, mdb, 0), dl = ddt;
	        var llm = hMap(lct, mlcb, 0);
	        wbits(out, p, nlc - 257);
	        wbits(out, p + 5, ndc - 1);
	        wbits(out, p + 10, nlcc - 4);
	        p += 14;
	        for (var i = 0; i < nlcc; ++i)
	            wbits(out, p + 3 * i, lct[clim[i]]);
	        p += 3 * nlcc;
	        var lcts = [lclt, lcdt];
	        for (var it = 0; it < 2; ++it) {
	            var clct = lcts[it];
	            for (var i = 0; i < clct.length; ++i) {
	                var len = clct[i] & 31;
	                wbits(out, p, llm[len]), p += lct[len];
	                if (len > 15)
	                    wbits(out, p, (clct[i] >> 5) & 127), p += clct[i] >> 12;
	            }
	        }
	    }
	    else {
	        lm = flm, ll = flt, dm = fdm, dl = fdt;
	    }
	    for (var i = 0; i < li; ++i) {
	        var sym = syms[i];
	        if (sym > 255) {
	            var len = (sym >> 18) & 31;
	            wbits16(out, p, lm[len + 257]), p += ll[len + 257];
	            if (len > 7)
	                wbits(out, p, (sym >> 23) & 31), p += fleb[len];
	            var dst = sym & 31;
	            wbits16(out, p, dm[dst]), p += dl[dst];
	            if (dst > 3)
	                wbits16(out, p, (sym >> 5) & 8191), p += fdeb[dst];
	        }
	        else {
	            wbits16(out, p, lm[sym]), p += ll[sym];
	        }
	    }
	    wbits16(out, p, lm[256]);
	    return p + ll[256];
	};
	// deflate options (nice << 13) | chain
	var deo = /*#__PURE__*/ new i32([65540, 131080, 131088, 131104, 262176, 1048704, 1048832, 2114560, 2117632]);
	// empty
	var et = /*#__PURE__*/ new u8(0);
	// compresses data into a raw DEFLATE buffer
	var dflt = function (dat, lvl, plvl, pre, post, st) {
	    var s = st.z || dat.length;
	    var o = new u8(pre + s + 5 * (1 + Math.ceil(s / 7000)) + post);
	    // writing to this writes to the output buffer
	    var w = o.subarray(pre, o.length - post);
	    var lst = st.l;
	    var pos = (st.r || 0) & 7;
	    if (lvl) {
	        if (pos)
	            w[0] = st.r >> 3;
	        var opt = deo[lvl - 1];
	        var n = opt >> 13, c = opt & 8191;
	        var msk_1 = (1 << plvl) - 1;
	        //    prev 2-byte val map    curr 2-byte val map
	        var prev = st.p || new u16(32768), head = st.h || new u16(msk_1 + 1);
	        var bs1_1 = Math.ceil(plvl / 3), bs2_1 = 2 * bs1_1;
	        var hsh = function (i) { return (dat[i] ^ (dat[i + 1] << bs1_1) ^ (dat[i + 2] << bs2_1)) & msk_1; };
	        // 24576 is an arbitrary number of maximum symbols per block
	        // 424 buffer for last block
	        var syms = new i32(25000);
	        // length/literal freq   distance freq
	        var lf = new u16(288), df = new u16(32);
	        //  l/lcnt  exbits  index          l/lind  waitdx          blkpos
	        var lc_1 = 0, eb = 0, i = st.i || 0, li = 0, wi = st.w || 0, bs = 0;
	        for (; i + 2 < s; ++i) {
	            // hash value
	            var hv = hsh(i);
	            // index mod 32768    previous index mod
	            var imod = i & 32767, pimod = head[hv];
	            prev[imod] = pimod;
	            head[hv] = imod;
	            // We always should modify head and prev, but only add symbols if
	            // this data is not yet processed ("wait" for wait index)
	            if (wi <= i) {
	                // bytes remaining
	                var rem = s - i;
	                if ((lc_1 > 7000 || li > 24576) && (rem > 423 || !lst)) {
	                    pos = wblk(dat, w, 0, syms, lf, df, eb, li, bs, i - bs, pos);
	                    li = lc_1 = eb = 0, bs = i;
	                    for (var j = 0; j < 286; ++j)
	                        lf[j] = 0;
	                    for (var j = 0; j < 30; ++j)
	                        df[j] = 0;
	                }
	                //  len    dist   chain
	                var l = 2, d = 0, ch_1 = c, dif = imod - pimod & 32767;
	                if (rem > 2 && hv == hsh(i - dif)) {
	                    var maxn = Math.min(n, rem) - 1;
	                    var maxd = Math.min(32767, i);
	                    // max possible length
	                    // not capped at dif because decompressors implement "rolling" index population
	                    var ml = Math.min(258, rem);
	                    while (dif <= maxd && --ch_1 && imod != pimod) {
	                        if (dat[i + l] == dat[i + l - dif]) {
	                            var nl = 0;
	                            for (; nl < ml && dat[i + nl] == dat[i + nl - dif]; ++nl)
	                                ;
	                            if (nl > l) {
	                                l = nl, d = dif;
	                                // break out early when we reach "nice" (we are satisfied enough)
	                                if (nl > maxn)
	                                    break;
	                                // now, find the rarest 2-byte sequence within this
	                                // length of literals and search for that instead.
	                                // Much faster than just using the start
	                                var mmd = Math.min(dif, nl - 2);
	                                var md = 0;
	                                for (var j = 0; j < mmd; ++j) {
	                                    var ti = i - dif + j & 32767;
	                                    var pti = prev[ti];
	                                    var cd = ti - pti & 32767;
	                                    if (cd > md)
	                                        md = cd, pimod = ti;
	                                }
	                            }
	                        }
	                        // check the previous match
	                        imod = pimod, pimod = prev[imod];
	                        dif += imod - pimod & 32767;
	                    }
	                }
	                // d will be nonzero only when a match was found
	                if (d) {
	                    // store both dist and len data in one int32
	                    // Make sure this is recognized as a len/dist with 28th bit (2^28)
	                    syms[li++] = 268435456 | (revfl[l] << 18) | revfd[d];
	                    var lin = revfl[l] & 31, din = revfd[d] & 31;
	                    eb += fleb[lin] + fdeb[din];
	                    ++lf[257 + lin];
	                    ++df[din];
	                    wi = i + l;
	                    ++lc_1;
	                }
	                else {
	                    syms[li++] = dat[i];
	                    ++lf[dat[i]];
	                }
	            }
	        }
	        for (i = Math.max(i, wi); i < s; ++i) {
	            syms[li++] = dat[i];
	            ++lf[dat[i]];
	        }
	        pos = wblk(dat, w, lst, syms, lf, df, eb, li, bs, i - bs, pos);
	        if (!lst) {
	            st.r = (pos & 7) | w[(pos / 8) | 0] << 3;
	            // shft(pos) now 1 less if pos & 7 != 0
	            pos -= 7;
	            st.h = head, st.p = prev, st.i = i, st.w = wi;
	        }
	    }
	    else {
	        for (var i = st.w || 0; i < s + lst; i += 65535) {
	            // end
	            var e = i + 65535;
	            if (e >= s) {
	                // write final block
	                w[(pos / 8) | 0] = lst;
	                e = s;
	            }
	            pos = wfblk(w, pos + 1, dat.subarray(i, e));
	        }
	        st.i = s;
	    }
	    return slc(o, 0, pre + shft(pos) + post);
	};
	// CRC32 table
	var crct = /*#__PURE__*/ (function () {
	    var t = new Int32Array(256);
	    for (var i = 0; i < 256; ++i) {
	        var c = i, k = 9;
	        while (--k)
	            c = ((c & 1) && -306674912) ^ (c >>> 1);
	        t[i] = c;
	    }
	    return t;
	})();
	// CRC32
	var crc = function () {
	    var c = -1;
	    return {
	        p: function (d) {
	            // closures have awful performance
	            var cr = c;
	            for (var i = 0; i < d.length; ++i)
	                cr = crct[(cr & 255) ^ d[i]] ^ (cr >>> 8);
	            c = cr;
	        },
	        d: function () { return ~c; }
	    };
	};
	// Adler32
	var adler = function () {
	    var a = 1, b = 0;
	    return {
	        p: function (d) {
	            // closures have awful performance
	            var n = a, m = b;
	            var l = d.length | 0;
	            for (var i = 0; i != l;) {
	                var e = Math.min(i + 2655, l);
	                for (; i < e; ++i)
	                    m += n += d[i];
	                n = (n & 65535) + 15 * (n >> 16), m = (m & 65535) + 15 * (m >> 16);
	            }
	            a = n, b = m;
	        },
	        d: function () {
	            a %= 65521, b %= 65521;
	            return (a & 255) << 24 | (a & 0xFF00) << 8 | (b & 255) << 8 | (b >> 8);
	        }
	    };
	};
	// deflate with opts
	var dopt = function (dat, opt, pre, post, st) {
	    if (!st) {
	        st = { l: 1 };
	        if (opt.dictionary) {
	            var dict = opt.dictionary.subarray(-32768);
	            var newDat = new u8(dict.length + dat.length);
	            newDat.set(dict);
	            newDat.set(dat, dict.length);
	            dat = newDat;
	            st.w = dict.length;
	        }
	    }
	    return dflt(dat, opt.level == null ? 6 : opt.level, opt.mem == null ? (st.l ? Math.ceil(Math.max(8, Math.min(13, Math.log(dat.length))) * 1.5) : 20) : (12 + opt.mem), pre, post, st);
	};
	// Walmart object spread
	var mrg = function (a, b) {
	    var o = {};
	    for (var k in a)
	        o[k] = a[k];
	    for (var k in b)
	        o[k] = b[k];
	    return o;
	};
	// worker clone
	// This is possibly the craziest part of the entire codebase, despite how simple it may seem.
	// The only parameter to this function is a closure that returns an array of variables outside of the function scope.
	// We're going to try to figure out the variable names used in the closure as strings because that is crucial for workerization.
	// We will return an object mapping of true variable name to value (basically, the current scope as a JS object).
	// The reason we can't just use the original variable names is minifiers mangling the toplevel scope.
	// This took me three weeks to figure out how to do.
	var wcln = function (fn, fnStr, td) {
	    var dt = fn();
	    var st = fn.toString();
	    var ks = st.slice(st.indexOf('[') + 1, st.lastIndexOf(']')).replace(/\s+/g, '').split(',');
	    for (var i = 0; i < dt.length; ++i) {
	        var v = dt[i], k = ks[i];
	        if (typeof v == 'function') {
	            fnStr += ';' + k + '=';
	            var st_1 = v.toString();
	            if (v.prototype) {
	                // for global objects
	                if (st_1.indexOf('[native code]') != -1) {
	                    var spInd = st_1.indexOf(' ', 8) + 1;
	                    fnStr += st_1.slice(spInd, st_1.indexOf('(', spInd));
	                }
	                else {
	                    fnStr += st_1;
	                    for (var t in v.prototype)
	                        fnStr += ';' + k + '.prototype.' + t + '=' + v.prototype[t].toString();
	                }
	            }
	            else
	                fnStr += st_1;
	        }
	        else
	            td[k] = v;
	    }
	    return fnStr;
	};
	var ch = [];
	// clone bufs
	var cbfs = function (v) {
	    var tl = [];
	    for (var k in v) {
	        if (v[k].buffer) {
	            tl.push((v[k] = new v[k].constructor(v[k])).buffer);
	        }
	    }
	    return tl;
	};
	// use a worker to execute code
	var wrkr = function (fns, init, id, cb) {
	    if (!ch[id]) {
	        var fnStr = '', td_1 = {}, m = fns.length - 1;
	        for (var i = 0; i < m; ++i)
	            fnStr = wcln(fns[i], fnStr, td_1);
	        ch[id] = { c: wcln(fns[m], fnStr, td_1), e: td_1 };
	    }
	    var td = mrg({}, ch[id].e);
	    return (0, node_worker_1.default)(ch[id].c + ';onmessage=function(e){for(var k in e.data)self[k]=e.data[k];onmessage=' + init.toString() + '}', id, td, cbfs(td), cb);
	};
	// base async inflate fn
	var bInflt = function () { return [u8, u16, i32, fleb, fdeb, clim, fl, fd, flrm, fdrm, rev, ec, hMap, max, bits, bits16, shft, slc, err, inflt, inflateSync, pbf, gopt]; };
	var bDflt = function () { return [u8, u16, i32, fleb, fdeb, clim, revfl, revfd, flm, flt, fdm, fdt, rev, deo, et, hMap, wbits, wbits16, hTree, ln, lc, clen, wfblk, wblk, shft, slc, dflt, dopt, deflateSync, pbf]; };
	// gzip extra
	var gze = function () { return [gzh, gzhl, wbytes, crc, crct]; };
	// gunzip extra
	var guze = function () { return [gzs, gzl]; };
	// zlib extra
	var zle = function () { return [zlh, wbytes, adler]; };
	// unzlib extra
	var zule = function () { return [zls]; };
	// post buf
	var pbf = function (msg) { return postMessage(msg, [msg.buffer]); };
	// get opts
	var gopt = function (o) { return o && {
	    out: o.size && new u8(o.size),
	    dictionary: o.dictionary
	}; };
	// async helper
	var cbify = function (dat, opts, fns, init, id, cb) {
	    var w = wrkr(fns, init, id, function (err, dat) {
	        w.terminate();
	        cb(err, dat);
	    });
	    w.postMessage([dat, opts], opts.consume ? [dat.buffer] : []);
	    return function () { w.terminate(); };
	};
	// auto stream
	var astrm = function (strm) {
	    strm.ondata = function (dat, final) { return postMessage([dat, final], [dat.buffer]); };
	    return function (ev) {
	        if (ev.data[0]) {
	            strm.push(ev.data[0], ev.data[1]);
	            postMessage([ev.data[0].length]);
	        }
	        else
	            strm.flush(ev.data[1]);
	    };
	};
	// async stream attach
	var astrmify = function (fns, strm, opts, init, id, flush, ext) {
	    var t;
	    var w = wrkr(fns, init, id, function (err, dat) {
	        if (err)
	            w.terminate(), strm.ondata.call(strm, err);
	        else if (!Array.isArray(dat))
	            ext(dat);
	        else if (dat.length == 1) {
	            strm.queuedSize -= dat[0];
	            if (strm.ondrain)
	                strm.ondrain(dat[0]);
	        }
	        else {
	            if (dat[1])
	                w.terminate();
	            strm.ondata.call(strm, err, dat[0], dat[1]);
	        }
	    });
	    w.postMessage(opts);
	    strm.queuedSize = 0;
	    strm.push = function (d, f) {
	        if (!strm.ondata)
	            err(5);
	        if (t)
	            strm.ondata(err(4, 0, 1), null, !!f);
	        strm.queuedSize += d.length;
	        // can fail for cross-realm Uint8Array, but ok - only a small performance penalty
	        w.postMessage([d, t = f], d.buffer instanceof ArrayBuffer ? [d.buffer] : []);
	    };
	    strm.terminate = function () { w.terminate(); };
	    if (flush) {
	        strm.flush = function (sync) { w.postMessage([0, sync]); };
	    }
	};
	// read 2 bytes
	var b2 = function (d, b) { return d[b] | (d[b + 1] << 8); };
	// read 4 bytes
	var b4 = function (d, b) { return (d[b] | (d[b + 1] << 8) | (d[b + 2] << 16) | (d[b + 3] << 24)) >>> 0; };
	// read 8 bytes
	var b8 = function (d, b) { return b4(d, b) + (b4(d, b + 4) * 4294967296); };
	// write bytes
	var wbytes = function (d, b, v) {
	    for (; v; ++b)
	        d[b] = v, v >>>= 8;
	};
	// gzip header
	var gzh = function (c, o) {
	    var fn = o.filename;
	    c[0] = 31, c[1] = 139, c[2] = 8, c[8] = o.level < 2 ? 4 : o.level == 9 ? 2 : 0, c[9] = 3; // assume Unix
	    if (o.mtime != 0)
	        wbytes(c, 4, Math.floor(new Date(o.mtime || Date.now()) / 1000));
	    if (fn) {
	        c[3] = 8;
	        for (var i = 0; i <= fn.length; ++i)
	            c[i + 10] = fn.charCodeAt(i);
	    }
	};
	// gzip footer: -8 to -4 = CRC, -4 to -0 is length
	// gzip start
	var gzs = function (d) {
	    if (d[0] != 31 || d[1] != 139 || d[2] != 8)
	        err(6, 'invalid gzip data');
	    var flg = d[3];
	    var st = 10;
	    if (flg & 4)
	        st += (d[10] | d[11] << 8) + 2;
	    for (var zs = (flg >> 3 & 1) + (flg >> 4 & 1); zs > 0; zs -= !d[st++])
	        ;
	    return st + (flg & 2);
	};
	// gzip length
	var gzl = function (d) {
	    var l = d.length;
	    return (d[l - 4] | d[l - 3] << 8 | d[l - 2] << 16 | d[l - 1] << 24) >>> 0;
	};
	// gzip header length
	var gzhl = function (o) { return 10 + (o.filename ? o.filename.length + 1 : 0); };
	// zlib header
	var zlh = function (c, o) {
	    var lv = o.level, fl = lv == 0 ? 0 : lv < 6 ? 1 : lv == 9 ? 3 : 2;
	    c[0] = 120, c[1] = (fl << 6) | (o.dictionary && 32);
	    c[1] |= 31 - ((c[0] << 8) | c[1]) % 31;
	    if (o.dictionary) {
	        var h = adler();
	        h.p(o.dictionary);
	        wbytes(c, 2, h.d());
	    }
	};
	// zlib start
	var zls = function (d, dict) {
	    if ((d[0] & 15) != 8 || (d[0] >> 4) > 7 || ((d[0] << 8 | d[1]) % 31))
	        err(6, 'invalid zlib data');
	    if ((d[1] >> 5 & 1) == +!dict)
	        err(6, 'invalid zlib data: ' + (d[1] & 32 ? 'need' : 'unexpected') + ' dictionary');
	    return (d[1] >> 3 & 4) + 2;
	};
	function StrmOpt(opts, cb) {
	    if (typeof opts == 'function')
	        cb = opts, opts = {};
	    this.ondata = cb;
	    return opts;
	}
	/**
	 * Streaming DEFLATE compression
	 */
	var Deflate = /*#__PURE__*/ (function () {
	    function Deflate(opts, cb) {
	        if (typeof opts == 'function')
	            cb = opts, opts = {};
	        this.ondata = cb;
	        this.o = opts || {};
	        this.s = { l: 0, i: 32768, w: 32768, z: 32768 };
	        // Buffer length must always be 0 mod 32768 for index calculations to be correct when modifying head and prev
	        // 98304 = 32768 (lookback) + 65536 (common chunk size)
	        this.b = new u8(98304);
	        if (this.o.dictionary) {
	            var dict = this.o.dictionary.subarray(-32768);
	            this.b.set(dict, 32768 - dict.length);
	            this.s.i = 32768 - dict.length;
	        }
	    }
	    Deflate.prototype.p = function (c, f) {
	        this.ondata(dopt(c, this.o, 0, 0, this.s), f);
	    };
	    /**
	     * Pushes a chunk to be deflated
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Deflate.prototype.push = function (chunk, final) {
	        if (!this.ondata)
	            err(5);
	        if (this.s.l)
	            err(4);
	        var endLen = chunk.length + this.s.z;
	        if (endLen > this.b.length) {
	            if (endLen > 2 * this.b.length - 32768) {
	                var newBuf = new u8(endLen & -32768);
	                newBuf.set(this.b.subarray(0, this.s.z));
	                this.b = newBuf;
	            }
	            var split = this.b.length - this.s.z;
	            this.b.set(chunk.subarray(0, split), this.s.z);
	            this.s.z = this.b.length;
	            this.p(this.b, false);
	            this.b.set(this.b.subarray(-32768));
	            this.b.set(chunk.subarray(split), 32768);
	            this.s.z = chunk.length - split + 32768;
	            this.s.i = 32766, this.s.w = 32768;
	        }
	        else {
	            this.b.set(chunk, this.s.z);
	            this.s.z += chunk.length;
	        }
	        this.s.l = final & 1;
	        if (this.s.z > this.s.w + 8191 || final) {
	            this.p(this.b, final || false);
	            this.s.w = this.s.i, this.s.i -= 2;
	        }
	        if (final) {
	            // cleanup unneeded buffers/state to reduce memory usage
	            this.s = this.o = {};
	            this.b = et;
	        }
	    };
	    /**
	     * Flushes buffered uncompressed data. Useful to immediately retrieve the
	     * deflated output for small inputs.
	     * @param sync Whether to flush to a byte boundary. A sync flush takes 4-5
	     *             extra bytes, but guarantees all pushed data is immediately
	     *             decompressible. A separate DEFLATE stream may be concatenated
	     *             with the current output after a sync flush.
	     */
	    Deflate.prototype.flush = function (sync) {
	        if (!this.ondata)
	            err(5);
	        if (this.s.l)
	            err(4);
	        this.p(this.b, false);
	        this.s.w = this.s.i, this.s.i -= 2;
	        // could technically skip writing the type-0 block for (this.s.r & 7) == 0,
	        // but the deterministic trailer (00 00 FF FF) is useful in some situations
	        if (sync) {
	            var c = new u8(6);
	            c[0] = this.s.r >> 3;
	            // write empty, non-final type-0 block
	            var ep = wfblk(c, this.s.r, et);
	            this.s.r = 0;
	            this.ondata(c.subarray(0, ep >> 3), false);
	        }
	    };
	    return Deflate;
	}());
	browser.Deflate = Deflate;
	/**
	 * Asynchronous streaming DEFLATE compression
	 */
	var AsyncDeflate = /*#__PURE__*/ (function () {
	    function AsyncDeflate(opts, cb) {
	        astrmify([
	            bDflt,
	            function () { return [astrm, Deflate]; }
	        ], this, StrmOpt.call(this, opts, cb), function (ev) {
	            var strm = new Deflate(ev.data);
	            onmessage = astrm(strm);
	        }, 6, 1);
	    }
	    return AsyncDeflate;
	}());
	browser.AsyncDeflate = AsyncDeflate;
	function deflate(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return cbify(data, opts, [
	        bDflt,
	    ], function (ev) { return pbf(deflateSync(ev.data[0], ev.data[1])); }, 0, cb);
	}
	/**
	 * Compresses data with DEFLATE without any wrapper
	 * @param data The data to compress
	 * @param opts The compression options
	 * @returns The deflated version of the data
	 */
	function deflateSync(data, opts) {
	    return dopt(data, opts || {}, 0, 0);
	}
	/**
	 * Streaming DEFLATE decompression
	 */
	var Inflate = /*#__PURE__*/ (function () {
	    function Inflate(opts, cb) {
	        // no StrmOpt here to avoid adding to workerizer
	        if (typeof opts == 'function')
	            cb = opts, opts = {};
	        this.ondata = cb;
	        var dict = opts && opts.dictionary && opts.dictionary.subarray(-32768);
	        this.s = { i: 0, b: dict ? dict.length : 0 };
	        this.o = new u8(32768);
	        this.p = new u8(0);
	        if (dict)
	            this.o.set(dict);
	    }
	    Inflate.prototype.e = function (c) {
	        if (!this.ondata)
	            err(5);
	        if (this.d)
	            err(4);
	        if (!this.p.length)
	            this.p = c;
	        else if (c.length) {
	            var n = new u8(this.p.length + c.length);
	            n.set(this.p), n.set(c, this.p.length), this.p = n;
	        }
	    };
	    Inflate.prototype.c = function (final) {
	        this.s.i = +(this.d = final || false);
	        var bts = this.s.b;
	        var dt = inflt(this.p, this.s, this.o);
	        this.ondata(slc(dt, bts, this.s.b), this.d);
	        this.o = slc(dt, this.s.b - 32768), this.s.b = this.o.length;
	        this.p = slc(this.p, (this.s.p / 8) | 0), this.s.p &= 7;
	    };
	    /**
	     * Pushes a chunk to be inflated
	     * @param chunk The chunk to push
	     * @param final Whether this is the final chunk
	     */
	    Inflate.prototype.push = function (chunk, final) {
	        this.e(chunk), this.c(final);
	    };
	    return Inflate;
	}());
	browser.Inflate = Inflate;
	/**
	 * Asynchronous streaming DEFLATE decompression
	 */
	var AsyncInflate = /*#__PURE__*/ (function () {
	    function AsyncInflate(opts, cb) {
	        astrmify([
	            bInflt,
	            function () { return [astrm, Inflate]; }
	        ], this, StrmOpt.call(this, opts, cb), function (ev) {
	            var strm = new Inflate(ev.data);
	            onmessage = astrm(strm);
	        }, 7, 0);
	    }
	    return AsyncInflate;
	}());
	browser.AsyncInflate = AsyncInflate;
	function inflate(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return cbify(data, opts, [
	        bInflt
	    ], function (ev) { return pbf(inflateSync(ev.data[0], gopt(ev.data[1]))); }, 1, cb);
	}
	function inflateSync(data, opts) {
	    return inflt(data, { i: 2 }, opts && opts.out, opts && opts.dictionary);
	}
	// before you yell at me for not just using extends, my reason is that TS inheritance is hard to workerize.
	/**
	 * Streaming GZIP compression
	 */
	var Gzip = /*#__PURE__*/ (function () {
	    function Gzip(opts, cb) {
	        this.c = crc();
	        this.l = 0;
	        this.v = 1;
	        Deflate.call(this, opts, cb);
	    }
	    /**
	     * Pushes a chunk to be GZIPped
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Gzip.prototype.push = function (chunk, final) {
	        this.c.p(chunk);
	        this.l += chunk.length;
	        Deflate.prototype.push.call(this, chunk, final);
	    };
	    Gzip.prototype.p = function (c, f) {
	        var raw = dopt(c, this.o, this.v && gzhl(this.o), f && 8, this.s);
	        if (this.v)
	            gzh(raw, this.o), this.v = 0;
	        if (f)
	            wbytes(raw, raw.length - 8, this.c.d()), wbytes(raw, raw.length - 4, this.l);
	        this.ondata(raw, f);
	    };
	    /**
	     * Flushes buffered uncompressed data. Useful to immediately retrieve the
	     * GZIPped output for small inputs.
	     * @param sync Whether to flush to a byte boundary. A sync flush takes 4-5
	     *             extra bytes, but guarantees all pushed data is immediately
	     *             decompressible.
	     */
	    Gzip.prototype.flush = function (sync) {
	        Deflate.prototype.flush.call(this, sync);
	    };
	    return Gzip;
	}());
	browser.Gzip = Gzip;
	browser.Compress = Gzip;
	/**
	 * Asynchronous streaming GZIP compression
	 */
	var AsyncGzip = /*#__PURE__*/ (function () {
	    function AsyncGzip(opts, cb) {
	        astrmify([
	            bDflt,
	            gze,
	            function () { return [astrm, Deflate, Gzip]; }
	        ], this, StrmOpt.call(this, opts, cb), function (ev) {
	            var strm = new Gzip(ev.data);
	            onmessage = astrm(strm);
	        }, 8, 1);
	    }
	    return AsyncGzip;
	}());
	browser.AsyncGzip = AsyncGzip;
	browser.AsyncCompress = AsyncGzip;
	function gzip(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return cbify(data, opts, [
	        bDflt,
	        gze,
	        function () { return [gzipSync]; }
	    ], function (ev) { return pbf(gzipSync(ev.data[0], ev.data[1])); }, 2, cb);
	}
	/**
	 * Compresses data with GZIP
	 * @param data The data to compress
	 * @param opts The compression options
	 * @returns The gzipped version of the data
	 */
	function gzipSync(data, opts) {
	    if (!opts)
	        opts = {};
	    var c = crc(), l = data.length;
	    c.p(data);
	    var d = dopt(data, opts, gzhl(opts), 8), s = d.length;
	    return gzh(d, opts), wbytes(d, s - 8, c.d()), wbytes(d, s - 4, l), d;
	}
	/**
	 * Streaming single or multi-member GZIP decompression
	 */
	var Gunzip = /*#__PURE__*/ (function () {
	    function Gunzip(opts, cb) {
	        this.v = 1;
	        this.r = 0;
	        Inflate.call(this, opts, cb);
	    }
	    /**
	     * Pushes a chunk to be GUNZIPped
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Gunzip.prototype.push = function (chunk, final) {
	        Inflate.prototype.e.call(this, chunk);
	        this.r += chunk.length;
	        if (this.v) {
	            var p = this.p.subarray(this.v - 1);
	            var s = p.length > 3 ? gzs(p) : 4;
	            if (s > p.length) {
	                if (!final)
	                    return;
	            }
	            else if (this.v > 1 && this.onmember) {
	                this.onmember(this.r - p.length);
	            }
	            this.p = p.subarray(s), this.v = 0;
	        }
	        // necessary to prevent TS from using the closure value
	        // This allows for workerization to function correctly
	        Inflate.prototype.c.call(this, 0);
	        // process concatenated GZIP
	        if (this.s.f && !this.s.l) {
	            this.v = shft(this.s.p) + 9;
	            this.s = { i: 0 };
	            this.o = new u8(0);
	            this.push(new u8(0), final);
	        }
	        else if (final) {
	            Inflate.prototype.c.call(this, final);
	        }
	    };
	    return Gunzip;
	}());
	browser.Gunzip = Gunzip;
	/**
	 * Asynchronous streaming single or multi-member GZIP decompression
	 */
	var AsyncGunzip = /*#__PURE__*/ (function () {
	    function AsyncGunzip(opts, cb) {
	        var _this = this;
	        astrmify([
	            bInflt,
	            guze,
	            function () { return [astrm, Inflate, Gunzip]; }
	        ], this, StrmOpt.call(this, opts, cb), function (ev) {
	            var strm = new Gunzip(ev.data);
	            strm.onmember = function (offset) { return postMessage(offset); };
	            onmessage = astrm(strm);
	        }, 9, 0, function (offset) { return _this.onmember && _this.onmember(offset); });
	    }
	    return AsyncGunzip;
	}());
	browser.AsyncGunzip = AsyncGunzip;
	function gunzip(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return cbify(data, opts, [
	        bInflt,
	        guze,
	        function () { return [gunzipSync]; }
	    ], function (ev) { return pbf(gunzipSync(ev.data[0], ev.data[1])); }, 3, cb);
	}
	function gunzipSync(data, opts) {
	    var st = gzs(data);
	    if (st + 8 > data.length)
	        err(6, 'invalid gzip data');
	    return inflt(data.subarray(st, -8), { i: 2 }, opts && opts.out || new u8(gzl(data)), opts && opts.dictionary);
	}
	/**
	 * Streaming Zlib compression
	 */
	var Zlib = /*#__PURE__*/ (function () {
	    function Zlib(opts, cb) {
	        this.c = adler();
	        this.v = 1;
	        Deflate.call(this, opts, cb);
	    }
	    /**
	     * Pushes a chunk to be zlibbed
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Zlib.prototype.push = function (chunk, final) {
	        this.c.p(chunk);
	        Deflate.prototype.push.call(this, chunk, final);
	    };
	    Zlib.prototype.p = function (c, f) {
	        var raw = dopt(c, this.o, this.v && (this.o.dictionary ? 6 : 2), f && 4, this.s);
	        if (this.v)
	            zlh(raw, this.o), this.v = 0;
	        if (f)
	            wbytes(raw, raw.length - 4, this.c.d());
	        this.ondata(raw, f);
	    };
	    /**
	     * Flushes buffered uncompressed data. Useful to immediately retrieve the
	     * zlibbed output for small inputs.
	     * @param sync Whether to flush to a byte boundary. A sync flush takes 4-5
	     *             extra bytes, but guarantees all pushed data is immediately
	     *             decompressible.
	     */
	    Zlib.prototype.flush = function (sync) {
	        Deflate.prototype.flush.call(this, sync);
	    };
	    return Zlib;
	}());
	browser.Zlib = Zlib;
	/**
	 * Asynchronous streaming Zlib compression
	 */
	var AsyncZlib = /*#__PURE__*/ (function () {
	    function AsyncZlib(opts, cb) {
	        astrmify([
	            bDflt,
	            zle,
	            function () { return [astrm, Deflate, Zlib]; }
	        ], this, StrmOpt.call(this, opts, cb), function (ev) {
	            var strm = new Zlib(ev.data);
	            onmessage = astrm(strm);
	        }, 10, 1);
	    }
	    return AsyncZlib;
	}());
	browser.AsyncZlib = AsyncZlib;
	function zlib(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return cbify(data, opts, [
	        bDflt,
	        zle,
	        function () { return [zlibSync]; }
	    ], function (ev) { return pbf(zlibSync(ev.data[0], ev.data[1])); }, 4, cb);
	}
	/**
	 * Compress data with Zlib
	 * @param data The data to compress
	 * @param opts The compression options
	 * @returns The zlib-compressed version of the data
	 */
	function zlibSync(data, opts) {
	    if (!opts)
	        opts = {};
	    var a = adler();
	    a.p(data);
	    var d = dopt(data, opts, opts.dictionary ? 6 : 2, 4);
	    return zlh(d, opts), wbytes(d, d.length - 4, a.d()), d;
	}
	/**
	 * Streaming Zlib decompression
	 */
	var Unzlib = /*#__PURE__*/ (function () {
	    function Unzlib(opts, cb) {
	        Inflate.call(this, opts, cb);
	        this.v = opts && opts.dictionary ? 2 : 1;
	    }
	    /**
	     * Pushes a chunk to be unzlibbed
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Unzlib.prototype.push = function (chunk, final) {
	        Inflate.prototype.e.call(this, chunk);
	        if (this.v) {
	            if (this.p.length < 6 && !final)
	                return;
	            this.p = this.p.subarray(zls(this.p, this.v - 1)), this.v = 0;
	        }
	        if (final) {
	            if (this.p.length < 4)
	                err(6, 'invalid zlib data');
	            this.p = this.p.subarray(0, -4);
	        }
	        // necessary to prevent TS from using the closure value
	        // This allows for workerization to function correctly
	        Inflate.prototype.c.call(this, final);
	    };
	    return Unzlib;
	}());
	browser.Unzlib = Unzlib;
	/**
	 * Asynchronous streaming Zlib decompression
	 */
	var AsyncUnzlib = /*#__PURE__*/ (function () {
	    function AsyncUnzlib(opts, cb) {
	        astrmify([
	            bInflt,
	            zule,
	            function () { return [astrm, Inflate, Unzlib]; }
	        ], this, StrmOpt.call(this, opts, cb), function (ev) {
	            var strm = new Unzlib(ev.data);
	            onmessage = astrm(strm);
	        }, 11, 0);
	    }
	    return AsyncUnzlib;
	}());
	browser.AsyncUnzlib = AsyncUnzlib;
	function unzlib(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return cbify(data, opts, [
	        bInflt,
	        zule,
	        function () { return [unzlibSync]; }
	    ], function (ev) { return pbf(unzlibSync(ev.data[0], gopt(ev.data[1]))); }, 5, cb);
	}
	function unzlibSync(data, opts) {
	    return inflt(data.subarray(zls(data, opts && opts.dictionary), -4), { i: 2 }, opts && opts.out, opts && opts.dictionary);
	}
	/**
	 * Streaming GZIP, Zlib, or raw DEFLATE decompression
	 */
	var Decompress = /*#__PURE__*/ (function () {
	    function Decompress(opts, cb) {
	        this.o = StrmOpt.call(this, opts, cb) || {};
	        this.G = Gunzip;
	        this.I = Inflate;
	        this.Z = Unzlib;
	    }
	    // init substream
	    // overriden by AsyncDecompress
	    Decompress.prototype.i = function () {
	        var _this = this;
	        this.s.ondata = function (dat, final) {
	            _this.ondata(dat, final);
	        };
	    };
	    /**
	     * Pushes a chunk to be decompressed
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Decompress.prototype.push = function (chunk, final) {
	        if (!this.ondata)
	            err(5);
	        if (!this.s) {
	            if (this.p && this.p.length) {
	                var n = new u8(this.p.length + chunk.length);
	                n.set(this.p), n.set(chunk, this.p.length);
	            }
	            else
	                this.p = chunk;
	            if (this.p.length > 2) {
	                this.s = (this.p[0] == 31 && this.p[1] == 139 && this.p[2] == 8)
	                    ? new this.G(this.o)
	                    : ((this.p[0] & 15) != 8 || (this.p[0] >> 4) > 7 || ((this.p[0] << 8 | this.p[1]) % 31))
	                        ? new this.I(this.o)
	                        : new this.Z(this.o);
	                this.i();
	                this.s.push(this.p, final);
	                this.p = null;
	            }
	        }
	        else
	            this.s.push(chunk, final);
	    };
	    return Decompress;
	}());
	browser.Decompress = Decompress;
	/**
	 * Asynchronous streaming GZIP, Zlib, or raw DEFLATE decompression
	 */
	var AsyncDecompress = /*#__PURE__*/ (function () {
	    function AsyncDecompress(opts, cb) {
	        Decompress.call(this, opts, cb);
	        this.queuedSize = 0;
	        this.G = AsyncGunzip;
	        this.I = AsyncInflate;
	        this.Z = AsyncUnzlib;
	    }
	    AsyncDecompress.prototype.i = function () {
	        var _this = this;
	        this.s.ondata = function (err, dat, final) {
	            _this.ondata(err, dat, final);
	        };
	        this.s.ondrain = function (size) {
	            _this.queuedSize -= size;
	            if (_this.ondrain)
	                _this.ondrain(size);
	        };
	    };
	    /**
	     * Pushes a chunk to be decompressed
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    AsyncDecompress.prototype.push = function (chunk, final) {
	        this.queuedSize += chunk.length;
	        Decompress.prototype.push.call(this, chunk, final);
	    };
	    return AsyncDecompress;
	}());
	browser.AsyncDecompress = AsyncDecompress;
	function decompress(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    return (data[0] == 31 && data[1] == 139 && data[2] == 8)
	        ? gunzip(data, opts, cb)
	        : ((data[0] & 15) != 8 || (data[0] >> 4) > 7 || ((data[0] << 8 | data[1]) % 31))
	            ? inflate(data, opts, cb)
	            : unzlib(data, opts, cb);
	}
	/**
	 * Expands compressed GZIP, Zlib, or raw DEFLATE data, automatically detecting the format
	 * @param data The data to decompress
	 * @param opts The decompression options
	 * @returns The decompressed version of the data
	 */
	function decompressSync(data, opts) {
	    return (data[0] == 31 && data[1] == 139 && data[2] == 8)
	        ? gunzipSync(data, opts)
	        : ((data[0] & 15) != 8 || (data[0] >> 4) > 7 || ((data[0] << 8 | data[1]) % 31))
	            ? inflateSync(data, opts)
	            : unzlibSync(data, opts);
	}
	// flatten a directory structure
	var fltn = function (d, p, t, o) {
	    for (var k in d) {
	        var val = d[k], n = p + k, op = o;
	        if (Array.isArray(val))
	            op = mrg(o, val[1]), val = val[0];
	        if (ArrayBuffer.isView(val))
	            t[n] = [val, op];
	        else {
	            t[n += '/'] = [new u8(0), op];
	            fltn(val, n, t, o);
	        }
	    }
	};
	// text encoder
	var te = typeof TextEncoder != 'undefined' && /*#__PURE__*/ new TextEncoder();
	// text decoder
	var td = typeof TextDecoder != 'undefined' && /*#__PURE__*/ new TextDecoder();
	// text decoder stream
	var tds = 0;
	try {
	    td.decode(et, { stream: true });
	    tds = 1;
	}
	catch (e) { }
	// decode UTF8
	var dutf8 = function (d) {
	    for (var r = '', i = 0;;) {
	        var c = d[i++];
	        var eb = (c > 127) + (c > 223) + (c > 239);
	        if (i + eb > d.length)
	            return { s: r, r: slc(d, i - 1) };
	        if (!eb)
	            r += String.fromCharCode(c);
	        else if (eb == 3) {
	            c = ((c & 15) << 18 | (d[i++] & 63) << 12 | (d[i++] & 63) << 6 | (d[i++] & 63)) - 65536,
	                r += String.fromCharCode(55296 | (c >> 10), 56320 | (c & 1023));
	        }
	        else if (eb & 1)
	            r += String.fromCharCode((c & 31) << 6 | (d[i++] & 63));
	        else
	            r += String.fromCharCode((c & 15) << 12 | (d[i++] & 63) << 6 | (d[i++] & 63));
	    }
	};
	/**
	 * Streaming UTF-8 decoding
	 */
	var DecodeUTF8 = /*#__PURE__*/ (function () {
	    /**
	     * Creates a UTF-8 decoding stream
	     * @param cb The callback to call whenever data is decoded
	     */
	    function DecodeUTF8(cb) {
	        this.ondata = cb;
	        if (tds)
	            this.t = new TextDecoder();
	        else
	            this.p = et;
	    }
	    /**
	     * Pushes a chunk to be decoded from UTF-8 binary
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    DecodeUTF8.prototype.push = function (chunk, final) {
	        if (!this.ondata)
	            err(5);
	        final = !!final;
	        if (this.t) {
	            this.ondata(this.t.decode(chunk, { stream: true }), final);
	            if (final) {
	                if (this.t.decode().length)
	                    err(8);
	                this.t = null;
	            }
	            return;
	        }
	        if (!this.p)
	            err(4);
	        var dat = new u8(this.p.length + chunk.length);
	        dat.set(this.p);
	        dat.set(chunk, this.p.length);
	        var _a = dutf8(dat), s = _a.s, r = _a.r;
	        if (final) {
	            if (r.length)
	                err(8);
	            this.p = null;
	        }
	        else
	            this.p = r;
	        this.ondata(s, final);
	    };
	    return DecodeUTF8;
	}());
	browser.DecodeUTF8 = DecodeUTF8;
	/**
	 * Streaming UTF-8 encoding
	 */
	var EncodeUTF8 = /*#__PURE__*/ (function () {
	    /**
	     * Creates a UTF-8 decoding stream
	     * @param cb The callback to call whenever data is encoded
	     */
	    function EncodeUTF8(cb) {
	        this.ondata = cb;
	    }
	    /**
	     * Pushes a chunk to be encoded to UTF-8
	     * @param chunk The string data to push
	     * @param final Whether this is the last chunk
	     */
	    EncodeUTF8.prototype.push = function (chunk, final) {
	        if (!this.ondata)
	            err(5);
	        if (this.d)
	            err(4);
	        this.ondata(strToU8(chunk), this.d = final || false);
	    };
	    return EncodeUTF8;
	}());
	browser.EncodeUTF8 = EncodeUTF8;
	/**
	 * Converts a string into a Uint8Array for use with compression/decompression methods
	 * @param str The string to encode
	 * @param latin1 Whether or not to interpret the data as Latin-1. This should
	 *               not need to be true unless decoding a binary string.
	 * @returns The string encoded in UTF-8/Latin-1 binary
	 */
	function strToU8(str, latin1) {
	    if (latin1) {
	        var ar_1 = new u8(str.length);
	        for (var i = 0; i < str.length; ++i)
	            ar_1[i] = str.charCodeAt(i);
	        return ar_1;
	    }
	    if (te)
	        return te.encode(str);
	    var l = str.length;
	    var ar = new u8(str.length + (str.length >> 1));
	    var ai = 0;
	    var w = function (v) { ar[ai++] = v; };
	    for (var i = 0; i < l; ++i) {
	        if (ai + 5 > ar.length) {
	            var n = new u8(ai + 8 + ((l - i) << 1));
	            n.set(ar);
	            ar = n;
	        }
	        var c = str.charCodeAt(i);
	        if (c < 128 || latin1)
	            w(c);
	        else if (c < 2048)
	            w(192 | (c >> 6)), w(128 | (c & 63));
	        else if (c > 55295 && c < 57344)
	            c = 65536 + (c & 1023 << 10) | (str.charCodeAt(++i) & 1023),
	                w(240 | (c >> 18)), w(128 | ((c >> 12) & 63)), w(128 | ((c >> 6) & 63)), w(128 | (c & 63));
	        else
	            w(224 | (c >> 12)), w(128 | ((c >> 6) & 63)), w(128 | (c & 63));
	    }
	    return slc(ar, 0, ai);
	}
	/**
	 * Converts a Uint8Array to a string
	 * @param dat The data to decode to string
	 * @param latin1 Whether or not to interpret the data as Latin-1. This should
	 *               not need to be true unless encoding to binary string.
	 * @returns The original UTF-8/Latin-1 string
	 */
	function strFromU8(dat, latin1) {
	    if (latin1) {
	        var r = '';
	        for (var i = 0; i < dat.length; i += 16384)
	            r += String.fromCharCode.apply(null, dat.subarray(i, i + 16384));
	        return r;
	    }
	    else if (td) {
	        return td.decode(dat);
	    }
	    else {
	        var _a = dutf8(dat), s = _a.s, r = _a.r;
	        if (r.length)
	            err(8);
	        return s;
	    }
	}
	// deflate bit flag
	var dbf = function (l) { return l == 1 ? 3 : l < 6 ? 2 : l == 9 ? 1 : 0; };
	// skip local zip header
	var slzh = function (d, b) { return b + 30 + b2(d, b + 26) + b2(d, b + 28); };
	// read zip header
	var zh = function (d, b, z) {
	    var fnl = b2(d, b + 28), efl = b2(d, b + 30), fn = strFromU8(d.subarray(b + 46, b + 46 + fnl), !(b2(d, b + 8) & 2048)), es = b + 46 + fnl;
	    var _a = z64hs(d, es, efl, z, b4(d, b + 20), b4(d, b + 24), b4(d, b + 42)), sc = _a[0], su = _a[1], off = _a[2];
	    return [b2(d, b + 10), sc, su, fn, es + efl + b2(d, b + 32), off];
	};
	// read zip64 header sizes
	var z64hs = function (d, b, l, z, sc, su, off) {
	    var nsc = sc == 4294967295, nsu = su == 4294967295, noff = off == 4294967295, e = b + l;
	    var nf = nsc + nsu + noff;
	    if (z && nf) {
	        for (; b + 4 < e; b += 4 + b2(d, b + 2)) {
	            if (b2(d, b) == 1) {
	                return [
	                    nsc ? b8(d, b + 4 + 8 * nsu) : sc,
	                    nsu ? b8(d, b + 4) : su,
	                    noff ? b8(d, b + 4 + 8 * (nsu + nsc)) : off,
	                    1
	                ];
	            }
	        }
	        // z == 2 for unknown whether or not zip64
	        if (z < 2)
	            err(13);
	    }
	    return [sc, su, off, 0];
	};
	// extra field length
	var exfl = function (ex) {
	    var le = 0;
	    if (ex) {
	        for (var k in ex) {
	            var l = ex[k].length;
	            if (l > 65535)
	                err(9);
	            le += l + 4;
	        }
	    }
	    return le;
	};
	// write zip header
	var wzh = function (d, b, f, fn, u, c, ce, co) {
	    var fl = fn.length, ex = f.extra, col = co && co.length;
	    var exl = exfl(ex);
	    wbytes(d, b, ce != null ? 0x2014B50 : 0x4034B50), b += 4;
	    if (ce != null)
	        d[b++] = 20, d[b++] = f.os;
	    d[b] = 20, b += 2; // spec compliance? what's that?
	    d[b++] = (f.flag << 1) | (c < 0 && 8), d[b++] = u && 8;
	    d[b++] = f.compression & 255, d[b++] = f.compression >> 8;
	    var dt = new Date(f.mtime == null ? Date.now() : f.mtime), y = dt.getFullYear() - 1980;
	    if (y < 0 || y > 119)
	        err(10);
	    wbytes(d, b, (y << 25) | ((dt.getMonth() + 1) << 21) | (dt.getDate() << 16) | (dt.getHours() << 11) | (dt.getMinutes() << 5) | (dt.getSeconds() >> 1)), b += 4;
	    if (c != -1) {
	        wbytes(d, b, f.crc);
	        wbytes(d, b + 4, c < 0 ? -c - 2 : c);
	        wbytes(d, b + 8, f.size);
	    }
	    wbytes(d, b + 12, fl);
	    wbytes(d, b + 14, exl), b += 16;
	    if (ce != null) {
	        wbytes(d, b, col);
	        wbytes(d, b + 6, f.attrs);
	        wbytes(d, b + 10, ce), b += 14;
	    }
	    d.set(fn, b);
	    b += fl;
	    if (exl) {
	        for (var k in ex) {
	            var exf = ex[k], l = exf.length;
	            wbytes(d, b, +k);
	            wbytes(d, b + 2, l);
	            d.set(exf, b + 4), b += 4 + l;
	        }
	    }
	    if (col)
	        d.set(co, b), b += col;
	    return b;
	};
	// write zip footer (end of central directory)
	var wzf = function (o, b, c, d, e) {
	    wbytes(o, b, 0x6054B50); // skip disk
	    wbytes(o, b + 8, c);
	    wbytes(o, b + 10, c);
	    wbytes(o, b + 12, d);
	    wbytes(o, b + 16, e);
	};
	/**
	 * A pass-through stream to keep data uncompressed in a ZIP archive.
	 */
	var ZipPassThrough = /*#__PURE__*/ (function () {
	    /**
	     * Creates a pass-through stream that can be added to ZIP archives
	     * @param filename The filename to associate with this data stream
	     */
	    function ZipPassThrough(filename) {
	        this.filename = filename;
	        this.c = crc();
	        this.size = 0;
	        this.compression = 0;
	    }
	    /**
	     * Processes a chunk and pushes to the output stream. You can override this
	     * method in a subclass for custom behavior, but by default this passes
	     * the data through. You must call this.ondata(err, chunk, final) at some
	     * point in this method.
	     * @param chunk The chunk to process
	     * @param final Whether this is the last chunk
	     */
	    ZipPassThrough.prototype.process = function (chunk, final) {
	        this.ondata(null, chunk, final);
	    };
	    /**
	     * Pushes a chunk to be added. If you are subclassing this with a custom
	     * compression algorithm, note that you must push data from the source
	     * file only, pre-compression.
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    ZipPassThrough.prototype.push = function (chunk, final) {
	        if (!this.ondata)
	            err(5);
	        this.c.p(chunk);
	        this.size += chunk.length;
	        if (final)
	            this.crc = this.c.d();
	        // we shouldn't really do this cast, but properly handling ArrayBufferLike
	        // makes the API unergonomic with Buffer
	        this.process(chunk, final || false);
	    };
	    return ZipPassThrough;
	}());
	browser.ZipPassThrough = ZipPassThrough;
	// I don't extend because TypeScript extension adds 1kB of runtime bloat
	/**
	 * Streaming DEFLATE compression for ZIP archives. Prefer using AsyncZipDeflate
	 * for better performance
	 */
	var ZipDeflate = /*#__PURE__*/ (function () {
	    /**
	     * Creates a DEFLATE stream that can be added to ZIP archives
	     * @param filename The filename to associate with this data stream
	     * @param opts The compression options
	     */
	    function ZipDeflate(filename, opts) {
	        var _this = this;
	        if (!opts)
	            opts = {};
	        ZipPassThrough.call(this, filename);
	        this.d = new Deflate(opts, function (dat, final) {
	            _this.ondata(null, dat, final);
	        });
	        this.compression = 8;
	        this.flag = dbf(opts.level);
	    }
	    ZipDeflate.prototype.process = function (chunk, final) {
	        try {
	            this.d.push(chunk, final);
	        }
	        catch (e) {
	            this.ondata(e, null, final);
	        }
	    };
	    /**
	     * Pushes a chunk to be deflated
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    ZipDeflate.prototype.push = function (chunk, final) {
	        ZipPassThrough.prototype.push.call(this, chunk, final);
	    };
	    return ZipDeflate;
	}());
	browser.ZipDeflate = ZipDeflate;
	/**
	 * Asynchronous streaming DEFLATE compression for ZIP archives
	 */
	var AsyncZipDeflate = /*#__PURE__*/ (function () {
	    /**
	     * Creates an asynchronous DEFLATE stream that can be added to ZIP archives
	     * @param filename The filename to associate with this data stream
	     * @param opts The compression options
	     */
	    function AsyncZipDeflate(filename, opts) {
	        var _this = this;
	        if (!opts)
	            opts = {};
	        ZipPassThrough.call(this, filename);
	        this.d = new AsyncDeflate(opts, function (err, dat, final) {
	            _this.ondata(err, dat, final);
	        });
	        this.compression = 8;
	        this.flag = dbf(opts.level);
	        this.terminate = this.d.terminate;
	    }
	    AsyncZipDeflate.prototype.process = function (chunk, final) {
	        this.d.push(chunk, final);
	    };
	    /**
	     * Pushes a chunk to be deflated
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    AsyncZipDeflate.prototype.push = function (chunk, final) {
	        ZipPassThrough.prototype.push.call(this, chunk, final);
	    };
	    return AsyncZipDeflate;
	}());
	browser.AsyncZipDeflate = AsyncZipDeflate;
	// TODO: Better tree shaking
	/**
	 * A zippable archive to which files can incrementally be added
	 */
	var Zip = /*#__PURE__*/ (function () {
	    /**
	     * Creates an empty ZIP archive to which files can be added
	     * @param cb The callback to call whenever data for the generated ZIP archive
	     *           is available
	     */
	    function Zip(cb) {
	        this.ondata = cb;
	        this.u = [];
	        this.d = 1;
	    }
	    /**
	     * Adds a file to the ZIP archive
	     * @param file The file stream to add
	     */
	    Zip.prototype.add = function (file) {
	        var _this = this;
	        if (!this.ondata)
	            err(5);
	        // finishing or finished
	        if (this.d & 2)
	            this.ondata(err(4 + (this.d & 1) * 8, 0, 1), null, false);
	        else {
	            var f = strToU8(file.filename), fl_1 = f.length;
	            var com = file.comment, o = com && strToU8(com);
	            var u = fl_1 != file.filename.length || (o && (com.length != o.length));
	            var hl_1 = fl_1 + exfl(file.extra) + 30;
	            if (fl_1 > 65535)
	                this.ondata(err(11, 0, 1), null, false);
	            var header = new u8(hl_1);
	            wzh(header, 0, file, f, u, -1);
	            var chks_1 = [header];
	            var pAll_1 = function () {
	                for (var _i = 0, chks_2 = chks_1; _i < chks_2.length; _i++) {
	                    var chk = chks_2[_i];
	                    _this.ondata(null, chk, false);
	                }
	                chks_1 = [];
	            };
	            var tr_1 = this.d;
	            this.d = 0;
	            var ind_1 = this.u.length;
	            var uf_1 = mrg(file, {
	                f: f,
	                u: u,
	                o: o,
	                t: function () {
	                    if (file.terminate)
	                        file.terminate();
	                },
	                r: function () {
	                    pAll_1();
	                    if (tr_1) {
	                        var nxt = _this.u[ind_1 + 1];
	                        if (nxt)
	                            nxt.r();
	                        else
	                            _this.d = 1;
	                    }
	                    tr_1 = 1;
	                }
	            });
	            var cl_1 = 0;
	            file.ondata = function (err, dat, final) {
	                if (err) {
	                    _this.ondata(err, dat, final);
	                    _this.terminate();
	                }
	                else {
	                    cl_1 += dat.length;
	                    chks_1.push(dat);
	                    if (final) {
	                        var dd = new u8(16);
	                        wbytes(dd, 0, 0x8074B50);
	                        wbytes(dd, 4, file.crc);
	                        wbytes(dd, 8, cl_1);
	                        wbytes(dd, 12, file.size);
	                        chks_1.push(dd);
	                        uf_1.c = cl_1, uf_1.b = hl_1 + cl_1 + 16, uf_1.crc = file.crc, uf_1.size = file.size;
	                        if (tr_1)
	                            uf_1.r();
	                        tr_1 = 1;
	                    }
	                    else if (tr_1)
	                        pAll_1();
	                }
	            };
	            this.u.push(uf_1);
	        }
	    };
	    /**
	     * Ends the process of adding files and prepares to emit the final chunks.
	     * This *must* be called after adding all desired files for the resulting
	     * ZIP file to work properly.
	     */
	    Zip.prototype.end = function () {
	        var _this = this;
	        if (this.d & 2) {
	            this.ondata(err(4 + (this.d & 1) * 8, 0, 1), null, true);
	            return;
	        }
	        if (this.d)
	            this.e();
	        else
	            this.u.push({
	                r: function () {
	                    if (!(_this.d & 1))
	                        return;
	                    _this.u.splice(-1, 1);
	                    _this.e();
	                },
	                t: function () { }
	            });
	        this.d = 3;
	    };
	    Zip.prototype.e = function () {
	        var bt = 0, l = 0, tl = 0;
	        for (var _i = 0, _a = this.u; _i < _a.length; _i++) {
	            var f = _a[_i];
	            tl += 46 + f.f.length + exfl(f.extra) + (f.o ? f.o.length : 0);
	        }
	        var out = new u8(tl + 22);
	        for (var _b = 0, _c = this.u; _b < _c.length; _b++) {
	            var f = _c[_b];
	            wzh(out, bt, f, f.f, f.u, -f.c - 2, l, f.o);
	            bt += 46 + f.f.length + exfl(f.extra) + (f.o ? f.o.length : 0), l += f.b;
	        }
	        wzf(out, bt, this.u.length, tl, l);
	        this.ondata(null, out, true);
	        this.d = 2;
	    };
	    /**
	     * A method to terminate any internal workers used by the stream. Subsequent
	     * calls to add() will fail.
	     */
	    Zip.prototype.terminate = function () {
	        for (var _i = 0, _a = this.u; _i < _a.length; _i++) {
	            var f = _a[_i];
	            f.t();
	        }
	        this.d = 2;
	    };
	    return Zip;
	}());
	browser.Zip = Zip;
	function zip(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    var r = {};
	    fltn(data, '', r, opts);
	    var k = Object.keys(r);
	    var lft = k.length, o = 0, tot = 0;
	    var slft = lft, files = new Array(lft);
	    var term = [];
	    var tAll = function () {
	        for (var i = 0; i < term.length; ++i)
	            term[i]();
	    };
	    var cbd = function (a, b) {
	        mt(function () { cb(a, b); });
	    };
	    mt(function () { cbd = cb; });
	    var cbf = function () {
	        var out = new u8(tot + 22), oe = o, cdl = tot - o;
	        tot = 0;
	        for (var i = 0; i < slft; ++i) {
	            var f = files[i];
	            try {
	                var l = f.c.length;
	                wzh(out, tot, f, f.f, f.u, l);
	                var badd = 30 + f.f.length + exfl(f.extra);
	                var loc = tot + badd;
	                out.set(f.c, loc);
	                wzh(out, o, f, f.f, f.u, l, tot, f.m), o += 16 + badd + (f.m ? f.m.length : 0), tot = loc + l;
	            }
	            catch (e) {
	                return cbd(e, null);
	            }
	        }
	        wzf(out, o, files.length, cdl, oe);
	        cbd(null, out);
	    };
	    if (!lft)
	        cbf();
	    var _loop_1 = function (i) {
	        var fn = k[i];
	        var _a = r[fn], file = _a[0], p = _a[1];
	        var c = crc(), size = file.length;
	        c.p(file);
	        var f = strToU8(fn), s = f.length;
	        var com = p.comment, m = com && strToU8(com), ms = m && m.length;
	        var exl = exfl(p.extra);
	        var compression = p.level == 0 ? 0 : 8;
	        var cbl = function (e, d) {
	            if (e) {
	                tAll();
	                cbd(e, null);
	            }
	            else {
	                var l = d.length;
	                files[i] = mrg(p, {
	                    size: size,
	                    crc: c.d(),
	                    c: d,
	                    f: f,
	                    m: m,
	                    u: s != fn.length || (m && (com.length != ms)),
	                    compression: compression
	                });
	                o += 30 + s + exl + l;
	                tot += 76 + 2 * (s + exl) + (ms || 0) + l;
	                if (!--lft)
	                    cbf();
	            }
	        };
	        if (s > 65535)
	            cbl(err(11, 0, 1), null);
	        if (!compression)
	            cbl(null, file);
	        else if (size < 160000) {
	            try {
	                cbl(null, deflateSync(file, p));
	            }
	            catch (e) {
	                cbl(e, null);
	            }
	        }
	        else
	            term.push(deflate(file, p, cbl));
	    };
	    // Cannot use lft because it can decrease
	    for (var i = 0; i < slft; ++i) {
	        _loop_1(i);
	    }
	    return tAll;
	}
	/**
	 * Synchronously creates a ZIP file. Prefer using `zip` for better performance
	 * with more than one file.
	 * @param data The directory structure for the ZIP archive
	 * @param opts The main options, merged with per-file options
	 * @returns The generated ZIP archive
	 */
	function zipSync(data, opts) {
	    if (!opts)
	        opts = {};
	    var r = {};
	    var files = [];
	    fltn(data, '', r, opts);
	    var o = 0;
	    var tot = 0;
	    for (var fn in r) {
	        var _a = r[fn], file = _a[0], p = _a[1];
	        var compression = p.level == 0 ? 0 : 8;
	        var f = strToU8(fn), s = f.length;
	        var com = p.comment, m = com && strToU8(com), ms = m && m.length;
	        var exl = exfl(p.extra);
	        if (s > 65535)
	            err(11);
	        var d = compression ? deflateSync(file, p) : file, l = d.length;
	        var c = crc();
	        c.p(file);
	        files.push(mrg(p, {
	            size: file.length,
	            crc: c.d(),
	            c: d,
	            f: f,
	            m: m,
	            u: s != fn.length || (m && (com.length != ms)),
	            o: o,
	            compression: compression
	        }));
	        o += 30 + s + exl + l;
	        tot += 76 + 2 * (s + exl) + (ms || 0) + l;
	    }
	    var out = new u8(tot + 22), oe = o, cdl = tot - o;
	    for (var i = 0; i < files.length; ++i) {
	        var f = files[i];
	        wzh(out, f.o, f, f.f, f.u, f.c.length);
	        var badd = 30 + f.f.length + exfl(f.extra);
	        out.set(f.c, f.o + badd);
	        wzh(out, o, f, f.f, f.u, f.c.length, f.o, f.m), o += 16 + badd + (f.m ? f.m.length : 0);
	    }
	    wzf(out, o, files.length, cdl, oe);
	    return out;
	}
	/**
	 * Streaming pass-through decompression for ZIP archives
	 */
	var UnzipPassThrough = /*#__PURE__*/ (function () {
	    function UnzipPassThrough() {
	    }
	    UnzipPassThrough.prototype.push = function (chunk, final) {
	        // same as ZipPassThrough: cast to retain Buffer ergonomics
	        this.ondata(null, chunk, final);
	    };
	    UnzipPassThrough.compression = 0;
	    return UnzipPassThrough;
	}());
	browser.UnzipPassThrough = UnzipPassThrough;
	/**
	 * Streaming DEFLATE decompression for ZIP archives. Prefer AsyncZipInflate for
	 * better performance.
	 */
	var UnzipInflate = /*#__PURE__*/ (function () {
	    /**
	     * Creates a DEFLATE decompression that can be used in ZIP archives
	     */
	    function UnzipInflate() {
	        var _this = this;
	        this.i = new Inflate(function (dat, final) {
	            _this.ondata(null, dat, final);
	        });
	    }
	    UnzipInflate.prototype.push = function (chunk, final) {
	        try {
	            this.i.push(chunk, final);
	        }
	        catch (e) {
	            this.ondata(e, null, final);
	        }
	    };
	    UnzipInflate.compression = 8;
	    return UnzipInflate;
	}());
	browser.UnzipInflate = UnzipInflate;
	/**
	 * Asynchronous streaming DEFLATE decompression for ZIP archives
	 */
	var AsyncUnzipInflate = /*#__PURE__*/ (function () {
	    /**
	     * Creates a DEFLATE decompression that can be used in ZIP archives
	     */
	    function AsyncUnzipInflate(_, sz) {
	        var _this = this;
	        if (sz < 320000) {
	            this.i = new Inflate(function (dat, final) {
	                _this.ondata(null, dat, final);
	            });
	        }
	        else {
	            this.i = new AsyncInflate(function (err, dat, final) {
	                _this.ondata(err, dat, final);
	            });
	            this.terminate = this.i.terminate;
	        }
	    }
	    AsyncUnzipInflate.prototype.push = function (chunk, final) {
	        if (this.i.terminate)
	            chunk = slc(chunk, 0);
	        this.i.push(chunk, final);
	    };
	    AsyncUnzipInflate.compression = 8;
	    return AsyncUnzipInflate;
	}());
	browser.AsyncUnzipInflate = AsyncUnzipInflate;
	/**
	 * A ZIP archive decompression stream that emits files as they are discovered
	 */
	var Unzip = /*#__PURE__*/ (function () {
	    /**
	     * Creates a ZIP decompression stream
	     * @param cb The callback to call whenever a file in the ZIP archive is found
	     */
	    function Unzip(cb) {
	        this.onfile = cb;
	        this.k = [];
	        this.o = {
	            0: UnzipPassThrough
	        };
	        this.p = et;
	    }
	    /**
	     * Pushes a chunk to be unzipped
	     * @param chunk The chunk to push
	     * @param final Whether this is the last chunk
	     */
	    Unzip.prototype.push = function (chunk, final) {
	        var _this = this;
	        if (!this.onfile)
	            err(5);
	        if (!this.p)
	            err(4);
	        if (this.c > 0) {
	            var len = Math.min(this.c, chunk.length);
	            var toAdd = chunk.subarray(0, len);
	            this.c -= len;
	            if (this.d)
	                this.d.push(toAdd, !this.c);
	            else
	                this.k[0].push(toAdd);
	            chunk = chunk.subarray(len);
	            if (chunk.length)
	                return this.push(chunk, final);
	        }
	        else {
	            var f = 0, i = 0, is = void 0, buf = void 0;
	            if (!this.p.length)
	                buf = chunk;
	            else if (!chunk.length)
	                buf = this.p;
	            else {
	                buf = new u8(this.p.length + chunk.length);
	                buf.set(this.p), buf.set(chunk, this.p.length);
	            }
	            var l = buf.length, oc = this.c, add = oc && this.d;
	            var _loop_2 = function () {
	                var sig = b4(buf, i);
	                if (sig == 0x4034B50) {
	                    f = 1, is = i;
	                    this_1.d = null;
	                    this_1.c = 0;
	                    var bf = b2(buf, i + 6), cmp_1 = b2(buf, i + 8), u = bf & 2048, dd = bf & 8, fnl = b2(buf, i + 26), es = b2(buf, i + 28);
	                    if (l > i + 30 + fnl + es) {
	                        var chks_3 = [];
	                        this_1.k.unshift(chks_3);
	                        f = 2;
	                        var lsc = b4(buf, i + 18), lsu = b4(buf, i + 22);
	                        var fn_1 = strFromU8(buf.subarray(i + 30, i += 30 + fnl), !u);
	                        var _a = z64hs(buf, i, es, 2, lsc, lsu, 0), sc_1 = _a[0], su_1 = _a[1], z64 = _a[3];
	                        if (dd)
	                            sc_1 = -1 - z64;
	                        i += es;
	                        this_1.c = sc_1;
	                        var d_1;
	                        var file_1 = {
	                            name: fn_1,
	                            compression: cmp_1,
	                            start: function () {
	                                if (!file_1.ondata)
	                                    err(5);
	                                if (!sc_1)
	                                    file_1.ondata(null, et, true);
	                                else {
	                                    var ctr = _this.o[cmp_1];
	                                    if (!ctr)
	                                        file_1.ondata(err(14, 'unknown compression type ' + cmp_1, 1), null, false);
	                                    d_1 = sc_1 < 0 ? new ctr(fn_1) : new ctr(fn_1, sc_1, su_1);
	                                    d_1.ondata = function (err, dat, final) { file_1.ondata(err, dat, final); };
	                                    for (var _i = 0, chks_4 = chks_3; _i < chks_4.length; _i++) {
	                                        var dat = chks_4[_i];
	                                        d_1.push(dat, false);
	                                    }
	                                    if (_this.k[0] == chks_3 && _this.c)
	                                        _this.d = d_1;
	                                    else
	                                        d_1.push(et, true);
	                                }
	                            },
	                            terminate: function () {
	                                if (d_1 && d_1.terminate)
	                                    d_1.terminate();
	                            }
	                        };
	                        if (sc_1 >= 0)
	                            file_1.size = sc_1, file_1.originalSize = su_1;
	                        this_1.onfile(file_1);
	                    }
	                    return "break";
	                }
	                else if (oc) {
	                    if (sig == 0x8074B50) {
	                        is = i += 12 + (oc == -2 && 8), f = 3, this_1.c = 0;
	                        return "break";
	                    }
	                    else if (sig == 0x2014B50) {
	                        is = i -= 4, f = 3, this_1.c = 0;
	                        return "break";
	                    }
	                }
	            };
	            var this_1 = this;
	            for (; i < l - 4; ++i) {
	                var state_1 = _loop_2();
	                if (state_1 === "break")
	                    break;
	            }
	            this.p = et;
	            if (oc < 0) {
	                var dat = f ? buf.subarray(0, is - 12 - (oc == -2 && 8) - (b4(buf, is - 16) == 0x8074B50 && 4)) : buf.subarray(0, i);
	                if (add)
	                    add.push(dat, !!f);
	                else
	                    this.k[+(f == 2)].push(dat);
	            }
	            if (f & 2)
	                return this.push(buf.subarray(i), final);
	            this.p = buf.subarray(i);
	        }
	        if (final) {
	            if (this.c)
	                err(13);
	            this.p = null;
	        }
	    };
	    /**
	     * Registers a decoder with the stream, allowing for files compressed with
	     * the compression type provided to be expanded correctly
	     * @param decoder The decoder constructor
	     */
	    Unzip.prototype.register = function (decoder) {
	        this.o[decoder.compression] = decoder;
	    };
	    return Unzip;
	}());
	browser.Unzip = Unzip;
	var mt = typeof queueMicrotask == 'function' ? queueMicrotask : typeof setTimeout == 'function' ? setTimeout : function (fn) { fn(); };
	function unzip(data, opts, cb) {
	    if (!cb)
	        cb = opts, opts = {};
	    if (typeof cb != 'function')
	        err(7);
	    var term = [];
	    var tAll = function () {
	        for (var i = 0; i < term.length; ++i)
	            term[i]();
	    };
	    var files = {};
	    var cbd = function (a, b) {
	        mt(function () { cb(a, b); });
	    };
	    mt(function () { cbd = cb; });
	    var e = data.length - 22;
	    for (; b4(data, e) != 0x6054B50; --e) {
	        if (!e || data.length - e > 65558) {
	            cbd(err(13, 0, 1), null);
	            return tAll;
	        }
	    }
	    var lft = b2(data, e + 8);
	    if (lft) {
	        var c = lft;
	        var o = b4(data, e + 16);
	        var z = b4(data, e - 20) == 0x7064B50;
	        if (z) {
	            var ze = b4(data, e - 12);
	            z = b4(data, ze) == 0x6064B50;
	            if (z) {
	                c = lft = b4(data, ze + 32);
	                o = b4(data, ze + 48);
	            }
	        }
	        var fltr = opts && opts.filter;
	        var _loop_3 = function (i) {
	            var _a = zh(data, o, z), c_1 = _a[0], sc = _a[1], su = _a[2], fn = _a[3], no = _a[4], off = _a[5], b = slzh(data, off);
	            o = no;
	            var cbl = function (e, d) {
	                if (e) {
	                    tAll();
	                    cbd(e, null);
	                }
	                else {
	                    if (d)
	                        files[fn] = d;
	                    if (!--lft)
	                        cbd(null, files);
	                }
	            };
	            if (!fltr || fltr({
	                name: fn,
	                size: sc,
	                originalSize: su,
	                compression: c_1
	            })) {
	                if (!c_1)
	                    cbl(null, slc(data, b, b + sc));
	                else if (c_1 == 8) {
	                    var infl = data.subarray(b, b + sc);
	                    // Synchronously decompress under 512KB, or barely-compressed data
	                    if (su < 524288 || sc > 0.8 * su) {
	                        try {
	                            cbl(null, inflateSync(infl, { out: new u8(su) }));
	                        }
	                        catch (e) {
	                            cbl(e, null);
	                        }
	                    }
	                    else
	                        term.push(inflate(infl, { size: su }, cbl));
	                }
	                else
	                    cbl(err(14, 'unknown compression type ' + c_1, 1), null);
	            }
	            else
	                cbl(null, null);
	        };
	        for (var i = 0; i < c; ++i) {
	            _loop_3(i);
	        }
	    }
	    else
	        cbd(null, {});
	    return tAll;
	}
	/**
	 * Synchronously decompresses a ZIP archive. Prefer using `unzip` for better
	 * performance with more than one file.
	 * @param data The raw compressed ZIP file
	 * @param opts The ZIP extraction options
	 * @returns The decompressed files
	 */
	function unzipSync(data, opts) {
	    var files = {};
	    var e = data.length - 22;
	    for (; b4(data, e) != 0x6054B50; --e) {
	        if (!e || data.length - e > 65558)
	            err(13);
	    }
	    var c = b2(data, e + 8);
	    if (!c)
	        return {};
	    var o = b4(data, e + 16);
	    var z = b4(data, e - 20) == 0x7064B50;
	    if (z) {
	        var ze = b4(data, e - 12);
	        z = b4(data, ze) == 0x6064B50;
	        if (z) {
	            c = b4(data, ze + 32);
	            o = b4(data, ze + 48);
	        }
	    }
	    var fltr = opts && opts.filter;
	    for (var i = 0; i < c; ++i) {
	        var _a = zh(data, o, z), c_2 = _a[0], sc = _a[1], su = _a[2], fn = _a[3], no = _a[4], off = _a[5], b = slzh(data, off);
	        o = no;
	        if (!fltr || fltr({
	            name: fn,
	            size: sc,
	            originalSize: su,
	            compression: c_2
	        })) {
	            if (!c_2)
	                files[fn] = slc(data, b, b + sc);
	            else if (c_2 == 8)
	                files[fn] = inflateSync(data.subarray(b, b + sc), { out: new u8(su) });
	            else
	                err(14, 'unknown compression type ' + c_2);
	        }
	    }
	    return files;
	}
	return browser;
}

var AltiumNameGenerator = {};

var AltiumAnnotationFileUtil = {};

var hasRequiredAltiumAnnotationFileUtil;

function requireAltiumAnnotationFileUtil () {
	if (hasRequiredAltiumAnnotationFileUtil) return AltiumAnnotationFileUtil;
	hasRequiredAltiumAnnotationFileUtil = 1;
	Object.defineProperty(AltiumAnnotationFileUtil, "__esModule", { value: true });
	AltiumAnnotationFileUtil.AltiumAnnotationFileUtil = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	const altiumValidation_1 = requireAltiumValidation();
	class AltiumAnnotationFileUtil$1 {
	    constructor(parsedDef, projectConfigUtil) {
	        this.projectConfigUtil = projectConfigUtil;
	        this.designatorRecordDiagnostics = [];
	        const multiPartIds = new Set();
	        const annotationFileSelection = this.selectAnnotationFiles(parsedDef);
	        this.annotationFiles = annotationFileSelection.files;
	        this.annotationFileSelectionDiagnostics = annotationFileSelection.diagnostics;
	        this.annotationFileSelectionBlockingDiagnostics = annotationFileSelection.blockingDiagnostics;
	        const allAnnotations = this.getDesignatorAnnotations();
	        for (const annotation of allAnnotations) {
	            if (annotation.LogicalPartID !== '1') {
	                multiPartIds.add(annotation.PhysicalDesignator);
	            }
	        }
	        for (const multiPartId of multiPartIds) {
	            for (const annotation of allAnnotations) {
	                if (annotation.PhysicalDesignator === multiPartId) {
	                    annotation._MultiPartId = true;
	                }
	            }
	        }
	        this.annotations = allAnnotations;
	        this.annotationsByUniqueId = this.buildAnnotationsByUniqueIdMap(allAnnotations);
	        this.channelNameByUniqueId = this.buildChannelNameMap(allAnnotations);
	        this.documentNameByUniqueId = this.buildDocumentNameMap(allAnnotations);
	        this.logicalReferenceDesignatorToAnnotation = this.buildLogicalReferenceDesignatorToAnnotationMap(allAnnotations);
	        this.compiledSheetNumberDiagnostics = [
	            ...this.annotationFileSelectionBlockingDiagnostics,
	            ...this.annotationFiles.flatMap(({ fileName, annotationFile }) => annotationFile === undefined ? [`Project-listed annotation file ${fileName} is missing or ambiguous`] : []),
	        ];
	        this.sheetNumberManagerDiagnostics = this.annotationFiles.flatMap(({ fileName, annotationFile }) => (annotationFile?.SheetNumberManager?.Diagnostics ?? []).map((diagnostic) => `${fileName}: ${diagnostic}`));
	    }
	    selectAnnotationFiles(parsedDef) {
	        const documentPathDiagnostics = this.projectConfigUtil.getAnnotationDocumentPathDiagnostics();
	        if (documentPathDiagnostics.length > 0) {
	            return {
	                files: [],
	                diagnostics: [...documentPathDiagnostics],
	                blockingDiagnostics: [...documentPathDiagnostics],
	            };
	        }
	        const annotationDescriptors = Object.values(parsedDef.other ?? {}).filter((file) => file.originalFileName.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase()));
	        const files = [];
	        const diagnostics = [];
	        const listedDescriptorNames = new Set();
	        for (const fileName of this.projectConfigUtil.getAnnotationDocumentPaths()) {
	            const matches = annotationDescriptors.filter((file) => {
	                const resolution = this.projectConfigUtil.resolveProjectDocumentPath(file.originalFileName);
	                return resolution.documentPath === fileName;
	            });
	            if (matches.length === 0) {
	                diagnostics.push(`annotation-manager-missing: ${fileName}`);
	                continue;
	            }
	            for (const match of matches) {
	                listedDescriptorNames.add(match.originalFileName);
	            }
	            files.push({ fileName, annotationFile: this.getSoleAnnotationFile(matches) });
	        }
	        const unlistedFileNames = annotationDescriptors
	            .filter((file) => !listedDescriptorNames.has(file.originalFileName))
	            .map((file) => file.originalFileName);
	        if (unlistedFileNames.length > 0) {
	            diagnostics.push(`annotation-manager-unlisted: ${unlistedFileNames.join(', ')}`);
	        }
	        if (files.length <= 1) {
	            return { files, diagnostics, blockingDiagnostics: [] };
	        }
	        const projectFileName = this.projectConfigUtil.projectFileName;
	        const projectStem = projectFileName === undefined ? undefined : this.getFileStem(projectFileName);
	        const projectNamedFiles = files.filter(({ fileName }) => this.getFileStem(fileName) === projectStem);
	        if (projectNamedFiles.length !== 1) {
	            const diagnostic = `annotation-manager-ambiguous: ${files.map(({ fileName }) => fileName).join(', ')}`;
	            return { files: [], diagnostics: [...diagnostics, diagnostic], blockingDiagnostics: [diagnostic] };
	        }
	        const selectedFile = projectNamedFiles[0];
	        for (const file of files) {
	            if (file !== selectedFile) {
	                diagnostics.push(`annotation-manager-ignored: ${file.fileName}`);
	            }
	        }
	        return { files: [selectedFile], diagnostics, blockingDiagnostics: [] };
	    }
	    getFileStem(fileName) {
	        const basename = sigma_engine_shared_1.Files.basenamePath(fileName.replace(/\\/g, '/'));
	        const extension = sigma_engine_shared_1.Files.extnamePath(basename);
	        return basename.slice(0, basename.length - extension.length);
	    }
	    getSoleAnnotationFile(files) {
	        if (files.length !== 1) {
	            return undefined;
	        }
	        return files[0].validationResult?.data?.metadata?.[altiumValidation_1.AltiumProjectMetadataKey.Annotation];
	    }
	    get allAnnotations() {
	        return this.annotations;
	    }
	    get numAnnotations() {
	        return this.annotations.length;
	    }
	    get sheetNumberDiagnostics() {
	        return [...this.compiledSheetNumberDiagnostics, ...this.sheetNumberManagerDiagnostics];
	    }
	    get annotationDiagnostics() {
	        return this.designatorRecordDiagnostics;
	    }
	    getCompiledSheetNumber(pathUniqueId, sourceFile) {
	        if (this.compiledSheetNumberDiagnostics.length > 0) {
	            return {
	                hasCompiledSheetNumberData: true,
	                diagnostic: `Invalid compiled sheet-number manager cannot resolve instance path ${pathUniqueId} and source document ${sourceFile}: ${this.compiledSheetNumberDiagnostics.join('; ')}`,
	            };
	        }
	        const documentName = this.projectConfigUtil.getAltiumDocumentConfiguration(sourceFile)?.DocumentPath;
	        const invalidRecords = this.annotationFiles[0]?.annotationFile?.SheetNumberManager?.InvalidSheetNumbers ?? [];
	        const invalidMatches = invalidRecords.filter((record) => (record.DocumentName !== undefined || record.UniqueIDPath !== undefined) &&
	            (record.DocumentName === undefined ||
	                this.normalizeSheetIdentity(record.DocumentName) === this.normalizeSheetIdentity(documentName)) &&
	            (record.UniqueIDPath === undefined ||
	                this.normalizeSheetIdentity(record.UniqueIDPath) === this.normalizeSheetIdentity(pathUniqueId)));
	        if (invalidMatches.length > 0) {
	            return {
	                hasCompiledSheetNumberData: true,
	                diagnostic: `Invalid compiled sheet-number assignment for instance path ${pathUniqueId} and source document ${sourceFile}; entries: ${invalidMatches.map((record) => record._Index).join(', ')}`,
	            };
	        }
	        const records = this.annotationFiles[0]?.annotationFile?.SheetNumberManager?.SheetNumbers ?? [];
	        let matches = records.filter((record) => record.DocumentName === documentName && record.UniqueIDPath === pathUniqueId);
	        let matchedPathDescription = `instance path ${pathUniqueId}`;
	        const separatorIndex = pathUniqueId.lastIndexOf('\\');
	        if (matches.length === 0 && separatorIndex >= 0) {
	            const parentPathUniqueId = pathUniqueId.substring(0, separatorIndex);
	            matchedPathDescription = `parent instance path ${parentPathUniqueId}`;
	            matches = records.filter((record) => {
	                const recordSeparatorIndex = record.UniqueIDPath.lastIndexOf('\\');
	                return (recordSeparatorIndex >= 0 &&
	                    record.UniqueIDPath.substring(0, recordSeparatorIndex) === parentPathUniqueId &&
	                    record.DocumentName === documentName);
	            });
	        }
	        const fileNames = this.annotationFiles.map(({ fileName }) => fileName).join(', ');
	        if (matches.length > 1) {
	            return {
	                hasCompiledSheetNumberData: true,
	                diagnostic: `Multiple compiled sheet-number records match ${matchedPathDescription} and source document ${sourceFile}; files: ${fileNames}`,
	            };
	        }
	        if (matches.length === 1) {
	            return { hasCompiledSheetNumberData: true, sheetNumber: matches[0].SheetNumber };
	        }
	        return {
	            hasCompiledSheetNumberData: false,
	            diagnostic: `No compiled sheet-number record matches instance path ${pathUniqueId} and source document ${sourceFile}`,
	        };
	    }
	    normalizeSheetIdentity(identity) {
	        return identity?.trim().replace(/[\\/]+/g, '/');
	    }
	    getAnnotationsByUniqueId(uniqueId) {
	        return Array.from(this.annotationsByUniqueId.get(uniqueId) ?? []);
	    }
	    getChannelNameByUniqueId(uniqueId) {
	        return this.channelNameByUniqueId.get(uniqueId);
	    }
	    getDocumentNameByUniqueId(uniqueId) {
	        return this.documentNameByUniqueId.get(uniqueId);
	    }
	    getAnnotationsByLogicalReferenceDesignator(referenceDesignator) {
	        return Array.from(this.logicalReferenceDesignatorToAnnotation.get(referenceDesignator) ?? []);
	    }
	    buildAnnotationsByUniqueIdMap(annotations) {
	        const map = new Map();
	        for (const annotation of annotations) {
	            const existing = map.get(annotation.UniqueID);
	            if (existing) {
	                existing.push(annotation);
	            }
	            else {
	                map.set(annotation.UniqueID, [annotation]);
	            }
	        }
	        return map;
	    }
	    buildChannelNameMap(annotations) {
	        const map = new Map();
	        const namesByChannel = new Map();
	        for (const annotation of annotations) {
	            const channelUniqueId = annotation.UniqueID.substring(0, annotation.UniqueID.lastIndexOf('\\'));
	            const names = namesByChannel.get(channelUniqueId) ?? new Set();
	            names.add(annotation.ChannelName);
	            namesByChannel.set(channelUniqueId, names);
	        }
	        for (const [channelUniqueId, names] of namesByChannel) {
	            if (names.size !== 1) {
	                this.designatorRecordDiagnostics.push(`annotation-channel-name-conflict: ${channelUniqueId}: ${[...names].sort().join(', ')}`);
	                continue;
	            }
	            for (const name of names) {
	                map.set(channelUniqueId, name);
	            }
	        }
	        return map;
	    }
	    buildDocumentNameMap(annotations) {
	        const map = new Map();
	        for (const annotation of annotations) {
	            if (!map.has(annotation.UniqueID)) {
	                map.set(annotation.UniqueID, annotation.DocumentName);
	            }
	        }
	        return map;
	    }
	    buildLogicalReferenceDesignatorToAnnotationMap(annotations) {
	        const map = new Map();
	        for (const annotation of annotations) {
	            const existing = map.get(annotation.LogicalDesignator);
	            if (existing) {
	                existing.push(annotation);
	            }
	            else {
	                map.set(annotation.LogicalDesignator, [annotation]);
	            }
	        }
	        return map;
	    }
	    getDesignatorAnnotations() {
	        this.designatorRecordDiagnostics.push(...this.annotationFileSelectionDiagnostics);
	        const selectedFile = this.annotationFiles[0];
	        if (selectedFile === undefined) {
	            return [];
	        }
	        if (selectedFile.annotationFile === undefined) {
	            this.designatorRecordDiagnostics.push(`Project-listed annotation file is missing or ambiguous: ${selectedFile.fileName}`);
	            return [];
	        }
	        const recordsByIdentity = new Map();
	        for (const [index, record] of selectedFile.annotationFile.DesignatorManager.entries()) {
	            if (record === undefined) {
	                this.designatorRecordDiagnostics.push(`annotation-manager-record-missing: ${index}`);
	                continue;
	            }
	            const identity = JSON.stringify([record.DocumentName, record.UniqueID, record.LogicalPartID]);
	            const records = recordsByIdentity.get(identity) ?? [];
	            records.push(record);
	            recordsByIdentity.set(identity, records);
	        }
	        const selectedAnnotations = [];
	        for (const [identity, records] of recordsByIdentity) {
	            if (records.length === 1) {
	                selectedAnnotations.push({ ...records[0] });
	            }
	            else {
	                this.designatorRecordDiagnostics.push(`Conflicting designator records for ${identity}; file: ${selectedFile.fileName}`);
	            }
	        }
	        return selectedAnnotations;
	    }
	}
	AltiumAnnotationFileUtil.AltiumAnnotationFileUtil = AltiumAnnotationFileUtil$1;
	
	return AltiumAnnotationFileUtil;
}

var PathUtils = {};

var hasRequiredPathUtils;

function requirePathUtils () {
	if (hasRequiredPathUtils) return PathUtils;
	hasRequiredPathUtils = 1;
	Object.defineProperty(PathUtils, "__esModule", { value: true });
	PathUtils.joinPathUniqueIds = joinPathUniqueIds;
	function joinPathUniqueIds(...pathUniqueIds) {
	    const joined = pathUniqueIds.filter((id) => id).join('\\');
	    if (!joined.includes('\\') && joined.length > 0) {
	        return `\\${joined}`;
	    }
	    return joined;
	}
	
	return PathUtils;
}

var hasRequiredAltiumNameGenerator;

function requireAltiumNameGenerator () {
	if (hasRequiredAltiumNameGenerator) return AltiumNameGenerator;
	hasRequiredAltiumNameGenerator = 1;
	Object.defineProperty(AltiumNameGenerator, "__esModule", { value: true });
	AltiumNameGenerator.AltiumNameGenerator = AltiumNameGenerator.NameGeneratorAnnotationSelectionMode = AltiumNameGenerator.NameGeneratorDesignatorType = AltiumNameGenerator.NameGeneratorNameType = void 0;
	AltiumNameGenerator.channelAlphaSuffix = channelAlphaSuffix;
	const sigma_engine_shared_1 = requireDist$1();
	const sigma_engine_shared_2 = requireDist$1();
	const AltiumAnnotationFileUtil_1 = requireAltiumAnnotationFileUtil();
	const _ = requireLodash();
	const PathUtils_1 = requirePathUtils();
	function channelAlphaSuffix(oneBasedIndex) {
	    let n = oneBasedIndex;
	    let suffix = '';
	    while (n > 0) {
	        const remainder = (n - 1) % 26;
	        suffix = String.fromCharCode('A'.charCodeAt(0) + remainder) + suffix;
	        n = Math.floor((n - 1) / 26);
	    }
	    return suffix;
	}
	var NameGeneratorNameType;
	(function (NameGeneratorNameType) {
	    NameGeneratorNameType[NameGeneratorNameType["Net"] = 0] = "Net";
	    NameGeneratorNameType[NameGeneratorNameType["Module"] = 1] = "Module";
	})(NameGeneratorNameType || (AltiumNameGenerator.NameGeneratorNameType = NameGeneratorNameType = {}));
	var NameGeneratorDesignatorType;
	(function (NameGeneratorDesignatorType) {
	    NameGeneratorDesignatorType[NameGeneratorDesignatorType["Physical"] = 0] = "Physical";
	    NameGeneratorDesignatorType[NameGeneratorDesignatorType["PhysicalInSchematic"] = 1] = "PhysicalInSchematic";
	    NameGeneratorDesignatorType[NameGeneratorDesignatorType["Logical"] = 2] = "Logical";
	})(NameGeneratorDesignatorType || (AltiumNameGenerator.NameGeneratorDesignatorType = NameGeneratorDesignatorType = {}));
	var NameGeneratorAnnotationSelectionMode;
	(function (NameGeneratorAnnotationSelectionMode) {
	    NameGeneratorAnnotationSelectionMode[NameGeneratorAnnotationSelectionMode["ExplicitOnly"] = 0] = "ExplicitOnly";
	    NameGeneratorAnnotationSelectionMode[NameGeneratorAnnotationSelectionMode["ExplicitOrChannel"] = 1] = "ExplicitOrChannel";
	    NameGeneratorAnnotationSelectionMode[NameGeneratorAnnotationSelectionMode["ExplicitFirstUnconsumedChannel"] = 2] = "ExplicitFirstUnconsumedChannel";
	})(NameGeneratorAnnotationSelectionMode || (AltiumNameGenerator.NameGeneratorAnnotationSelectionMode = NameGeneratorAnnotationSelectionMode = {}));
	class AltiumNameGenerator$1 {
	    constructor(model, parsedDef, projectConfigUtil, annotationUtil, processingResultSink) {
	        this.model = model;
	        this.parsedDef = parsedDef;
	        this.projectConfigUtil = projectConfigUtil;
	        this.processingResultSink = processingResultSink;
	        this.annotations = [];
	        this.processingResults = [];
	        this.refToNameMap = new Map();
	        this.physicalNameToRefMap = new Map();
	        this.physicalAnnotationClaims = new Map();
	        this.ambiguousPhysicalAnnotationClaims = new Map();
	        this.rejectedIndexedAnnotationIdentityMismatches = new Set();
	        this.rejectedIndexedAnnotationIdentityMismatchExamples = [];
	        this.claimedAnnotations = new Map();
	        this.uniqueIdClaimedAnnotations = new Map();
	        this.annotationUtil = annotationUtil ?? new AltiumAnnotationFileUtil_1.AltiumAnnotationFileUtil(parsedDef, projectConfigUtil);
	        this.annotations = [...this.annotationUtil.allAnnotations];
	    }
	    get allAnnotations() {
	        return this.annotations;
	    }
	    get numAnnotations() {
	        return this.annotations.length;
	    }
	    getAnnotationEntryForPart(sheet, variables, enableFallbacks = false) {
	        let matchingAnnotations = [];
	        const logicalPart = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variables, 'Component', true);
	        const uniqueId = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variables, 'UniqueID', true);
	        const partId = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variables, 'PartId')?.toString() ?? '1';
	        const completeUniqueId = (0, PathUtils_1.joinPathUniqueIds)(sheet.pathUniqueId ?? '', uniqueId);
	        const claimedAnnotation = this.uniqueIdClaimedAnnotations.get(completeUniqueId);
	        if (claimedAnnotation !== undefined &&
	            this.matchesPartIdentity(claimedAnnotation, sheet, logicalPart, partId, completeUniqueId)) {
	            this.reportLogicalLabelDifference(claimedAnnotation, logicalPart, completeUniqueId);
	            return claimedAnnotation;
	        }
	        matchingAnnotations = this.annotationUtil.getAnnotationsByUniqueId(completeUniqueId);
	        if (matchingAnnotations.length === 0 && enableFallbacks) {
	            if (logicalPart) {
	                const allForDesignator = this.annotationUtil.getAnnotationsByLogicalReferenceDesignator(logicalPart);
	                if (matchingAnnotations.length === 0) {
	                    matchingAnnotations = allForDesignator.filter((a) => a.LogicalDesignator === logicalPart &&
	                        a.UniqueID.endsWith(`\\${uniqueId}`) &&
	                        a.DocumentName === sheet.filename &&
	                        a.ChannelName === sheet.roomName &&
	                        partId === a.LogicalPartID);
	                }
	                if (matchingAnnotations.length === 0) {
	                    matchingAnnotations = allForDesignator.filter((a) => a.LogicalDesignator === logicalPart &&
	                        a.UniqueID.endsWith(`\\${uniqueId}`) &&
	                        a.DocumentName === sheet.filename &&
	                        partId === a.LogicalPartID);
	                }
	                if (matchingAnnotations.length === 0) {
	                    matchingAnnotations = allForDesignator.filter((a) => a.LogicalDesignator === logicalPart && a.UniqueID.endsWith(`\\${uniqueId}`) && partId === a.LogicalPartID);
	                }
	                for (const candidate of matchingAnnotations) {
	                    if (candidate.DocumentName !== sheet.filename || candidate.ChannelName !== sheet.roomName) {
	                        continue;
	                    }
	                    this.canClaimPhysicalAnnotation(completeUniqueId, candidate, logicalPart, partId, true);
	                }
	                if (matchingAnnotations.length > 1) {
	                    this.model.warnings.push(`No annotation found for part ${logicalPart} with unique ID ${completeUniqueId} in document ${sheet.filename} (${sheet.uniqueId}). Found ${matchingAnnotations.length} matches without document or channel name match.`);
	                    return undefined;
	                }
	            }
	            if (matchingAnnotations.length > 0) {
	                this.model.warnings.push(`No annotation found for part ${logicalPart} with unique ID ${completeUniqueId} in document ${sheet.filename} (${sheet.uniqueId}). Trying fallback.`);
	            }
	        }
	        const indexedCandidates = matchingAnnotations.filter((annotation) => annotation._Index !== undefined);
	        const indexedAnnotations = indexedCandidates.filter((annotation) => this.matchesPartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId));
	        const rejectedIndexedAnnotations = indexedCandidates.filter((annotation) => !indexedAnnotations.includes(annotation));
	        if (rejectedIndexedAnnotations.length > 0) {
	            this.recordRejectedIndexedAnnotationIdentityMismatch(rejectedIndexedAnnotations, logicalPart, completeUniqueId, sheet.filename);
	        }
	        const indexlessAnnotations = matchingAnnotations.filter((annotation) => annotation._Index === undefined);
	        if (indexlessAnnotations.length > 0) {
	            const matchingIndexlessAnnotations = indexlessAnnotations.filter((annotation) => this.matchesCompletePartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId));
	            if (matchingIndexlessAnnotations.length !== 1) {
	                this.model.warnings.push(`Cannot resolve indexless annotation for part ${logicalPart} with unique ID ${uniqueId}. Found ${matchingIndexlessAnnotations.length} full-identity matches.`);
	            }
	            if (matchingIndexlessAnnotations.length > 0 && indexedAnnotations.length > 0) {
	                this.model.warnings.push(`Conflicting indexed and indexless annotations for part ${logicalPart} with unique ID ${uniqueId}. Indexed physical designators: ${indexedAnnotations.map((annotation) => annotation.PhysicalDesignator).join(', ')}; indexless physical designators: ${matchingIndexlessAnnotations.map((annotation) => annotation.PhysicalDesignator).join(', ')}.`);
	                return undefined;
	            }
	            matchingAnnotations = [
	                ...indexedAnnotations,
	                ...(matchingIndexlessAnnotations.length === 1 ? matchingIndexlessAnnotations : []),
	            ];
	        }
	        else {
	            matchingAnnotations = indexedAnnotations;
	        }
	        if (matchingAnnotations.length > 1) {
	            this.model.warnings.push(`Multiple annotations found for part ${logicalPart} with unique ID ${uniqueId}. No annotation selected.`);
	            return undefined;
	        }
	        if (matchingAnnotations?.length) {
	            const useAnnotation = matchingAnnotations[0];
	            if (!this.canClaimPhysicalAnnotation(completeUniqueId, useAnnotation, logicalPart, partId)) {
	                return undefined;
	            }
	            this.reportLogicalLabelDifference(useAnnotation, logicalPart, completeUniqueId);
	            this.claimedAnnotations.set(useAnnotation, sigma_engine_shared_2.Utils.resolveThunkMap(variables));
	            this.uniqueIdClaimedAnnotations.set(completeUniqueId, useAnnotation);
	            return useAnnotation;
	        }
	        return undefined;
	    }
	    recordRejectedIndexedAnnotationIdentityMismatch(rejectedAnnotations, logicalPart, completeUniqueId, documentName) {
	        const initialCount = this.rejectedIndexedAnnotationIdentityMismatches.size;
	        for (const annotation of rejectedAnnotations) {
	            this.rejectedIndexedAnnotationIdentityMismatches.add(JSON.stringify([annotation._Index, annotation.DocumentName, annotation.UniqueID, annotation.LogicalPartID]));
	        }
	        if (this.rejectedIndexedAnnotationIdentityMismatches.size === initialCount) {
	            return;
	        }
	        if (this.rejectedIndexedAnnotationIdentityMismatchExamples.length < 3) {
	            this.rejectedIndexedAnnotationIdentityMismatchExamples.push(`part ${this.truncateDiagnosticValue(logicalPart ?? '')} with unique ID ${this.truncateDiagnosticValue(completeUniqueId)} in document ${this.truncateDiagnosticValue(documentName)}`);
	        }
	        const message = `Rejected indexed annotation identity mismatch; ${this.rejectedIndexedAnnotationIdentityMismatches.size} record(s) rejected. ` +
	            `Examples: ${this.rejectedIndexedAnnotationIdentityMismatchExamples.join('; ')}`;
	        if (this.rejectedIndexedAnnotationIdentityMismatchResult) {
	            this.rejectedIndexedAnnotationIdentityMismatchResult.message = message;
	            if (this.rejectedIndexedAnnotationIdentityMismatchWarningIndex !== undefined) {
	                this.model.warnings[this.rejectedIndexedAnnotationIdentityMismatchWarningIndex] = message;
	            }
	            return;
	        }
	        this.rejectedIndexedAnnotationIdentityMismatchResult = {
	            type: 'altium-name-generator',
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            message,
	        };
	        this.recordProcessingResult(this.rejectedIndexedAnnotationIdentityMismatchResult);
	        this.rejectedIndexedAnnotationIdentityMismatchWarningIndex = this.model.warnings.length;
	        this.model.warnings.push(message);
	    }
	    truncateDiagnosticValue(value) {
	        const maximumLength = 96;
	        return value.length <= maximumLength ? value : `${value.slice(0, maximumLength - 1)}…`;
	    }
	    reportLogicalLabelDifference(annotation, logicalPart, completeUniqueId) {
	        if (annotation.LogicalDesignator !== logicalPart) {
	            this.model.warnings.push(`Saved logical designator ${annotation.LogicalDesignator} differs from ${logicalPart} for unique ID ${completeUniqueId}; resolved by exact saved instance identity.`);
	        }
	    }
	    matchesPartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId) {
	        return ((annotation._Index !== undefined || annotation.LogicalDesignator === logicalPart) &&
	            annotation.LogicalPartID === partId &&
	            annotation.DocumentName === sheet.filename &&
	            annotation.ChannelName === sheet.roomName &&
	            annotation.UniqueID === completeUniqueId);
	    }
	    matchesCompletePartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId) {
	        return (annotation.LogicalDesignator === logicalPart &&
	            annotation.LogicalPartID === partId &&
	            annotation.DocumentName === sheet.filename &&
	            annotation.ChannelName === sheet.roomName &&
	            annotation.UniqueID === completeUniqueId);
	    }
	    canClaimPhysicalAnnotation(forModuleRef, annotation, logicalPart, partId, isReservation = false) {
	        const claims = this.physicalAnnotationClaims.get(annotation.PhysicalDesignator) ?? new Map();
	        const claimKey = JSON.stringify([annotation.DocumentName, annotation.ChannelName, forModuleRef, partId]);
	        claims.set(claimKey, {
	            logicalPart,
	            partId,
	            moduleRef: forModuleRef,
	            documentName: annotation.DocumentName,
	            channelName: annotation.ChannelName,
	            isReservation,
	        });
	        this.physicalAnnotationClaims.set(annotation.PhysicalDesignator, claims);
	        const conflictingClaims = Array.from(claims).filter(([key, claim]) => key !== claimKey &&
	            (!(isReservation || claim.isReservation) ||
	                (claim.documentName === annotation.DocumentName && claim.channelName === annotation.ChannelName)) &&
	            (claim.partId === partId || claim.logicalPart !== logicalPart));
	        const ambiguousClaims = this.ambiguousPhysicalAnnotationClaims.get(annotation.PhysicalDesignator) ?? new Set();
	        if (conflictingClaims.length > 0) {
	            ambiguousClaims.add(claimKey);
	            for (const [key] of conflictingClaims) {
	                ambiguousClaims.add(key);
	            }
	            this.ambiguousPhysicalAnnotationClaims.set(annotation.PhysicalDesignator, ambiguousClaims);
	        }
	        if (ambiguousClaims.has(claimKey)) {
	            const rejectedClaims = Array.from(claims).filter(([key]) => ambiguousClaims.has(key));
	            for (const [, { moduleRef: ref }] of rejectedClaims) {
	                this.uniqueIdClaimedAnnotations.delete(ref);
	                const name = this.refToNameMap.get(ref);
	                if (name?.physical === annotation.PhysicalDesignator) {
	                    if (this.physicalNameToRefMap.get(annotation.PhysicalDesignator) === name) {
	                        this.physicalNameToRefMap.delete(annotation.PhysicalDesignator);
	                    }
	                    this.refToNameMap.delete(ref);
	                }
	            }
	            this.model.warnings.push(`Conflicting physical annotation ${annotation.PhysicalDesignator}; claimants: ${rejectedClaims.map(([, claim]) => `${claim.logicalPart} (${claim.moduleRef}, part ${claim.partId}, document ${claim.documentName}, channel ${claim.channelName})`).join('; ')}. No saved mapping selected.`);
	            return false;
	        }
	        if (isReservation) {
	            return true;
	        }
	        const existingName = this.physicalNameToRefMap.get(annotation.PhysicalDesignator);
	        if (existingName?.logicalMultiPart.length &&
	            existingName.logical === logicalPart &&
	            existingName.physical === annotation.PhysicalDesignator) {
	            return true;
	        }
	        const existingRef = Array.from(this.refToNameMap.entries()).find(([, name]) => name === existingName)?.[0];
	        if (existingRef !== undefined && existingRef !== forModuleRef) {
	            this.model.warnings.push(`Physical annotation ${annotation.PhysicalDesignator} is already claimed by ${existingRef}; rejected claimant ${forModuleRef}.`);
	            return false;
	        }
	        return true;
	    }
	    generateName(oldName, sheet, variables, annotationSelectionMode, designatorType, nameType) {
	        const variablesThunkMap = this.initVariableThunkMap(sheet, oldName, variables);
	        if (nameType === NameGeneratorNameType.Module) {
	            const name = this.getAnnotationsForModule(oldName, sheet, variables, designatorType, true);
	            if (name) {
	                return name;
	            }
	            if (annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitOnly) {
	                return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
	                    logical: oldName,
	                    physical: oldName,
	                    physicalInSchematic: oldName,
	                    isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
	                }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
	            }
	            if (annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitFirstUnconsumedChannel) {
	                if (this.canUseComponentPhysicalName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), oldName, designatorType)) {
	                    return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
	                        logical: oldName,
	                        physical: oldName,
	                        physicalInSchematic: oldName,
	                        isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
	                    }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
	                }
	            }
	        }
	        else {
	            if (annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitFirstUnconsumedChannel ||
	                annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitOnly) {
	                throw new Error('ExplicitFirstUnconsumedChannel and ExplicitOnly are only supported for module names, not nets');
	            }
	        }
	        if (sheet.numInstances === 1) {
	            const effectiveTotalInstances = sheet.designWideInstances ?? sheet.totalSheetInstances;
	            if (nameType === NameGeneratorNameType.Module) {
	                if (effectiveTotalInstances === 1) {
	                    if (nameType === NameGeneratorNameType.Module) {
	                        return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
	                            logical: oldName,
	                            physical: oldName,
	                            physicalInSchematic: oldName,
	                            isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
	                        }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
	                    }
	                }
	            }
	            if (nameType === NameGeneratorNameType.Net) {
	                if (effectiveTotalInstances === 1) {
	                    return oldName;
	                }
	            }
	        }
	        const name = this.annotateReferenceDesignatorUsingChannelDesignatorFormatString(oldName, variablesThunkMap);
	        if (nameType === NameGeneratorNameType.Module) {
	            return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
	                logical: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'Component', true),
	                physical: name,
	                physicalInSchematic: name,
	                isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
	            }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
	        }
	        return name;
	    }
	    getAnnotationsForModule(designator, sheet, variables, designatorType, enableFallbacks) {
	        const variablesThunkMap = this.initVariableThunkMap(sheet, designator, variables);
	        let name;
	        const annotation = this.getAnnotationEntryForPart(sheet, variablesThunkMap, enableFallbacks);
	        if (annotation) {
	            return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
	                logical: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'Component', true),
	                physical: annotation.PhysicalDesignator,
	                physicalInSchematic: annotation.PhysicalDesignator,
	                isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
	            }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
	        }
	        return name;
	    }
	    initVariableThunkMap(sheet, oldName, variables) {
	        return {
	            DocumentName: () => sheet.name,
	            ChannelAlpha: () => {
	                if (typeof sheet?.channelAlpha !== 'undefined') {
	                    return sheet.channelAlpha.toString();
	                }
	                return channelAlphaSuffix(sheet.physicalChannelIndex);
	            },
	            ChannelIndex: () => {
	                return sheet.physicalChannelIndex.toString();
	            },
	            ChannelPrefix: () => {
	                if (typeof sheet?.channelPrefix !== 'undefined') {
	                    return sheet.channelPrefix.toString();
	                }
	                return sheet.name;
	            },
	            RoomName: () => {
	                if (typeof sheet?.roomName !== 'undefined') {
	                    return sheet.roomName.toString();
	                }
	                const warning = `Could not determine $RoomName when re-annotating: ChannelRoomNamingStyle is not implemented.`;
	                if (!this.model.warnings.includes(warning)) {
	                    this.model.warnings.push(warning);
	                }
	                return sheet.name;
	            },
	            Component: oldName,
	            ...(variables ?? {}),
	        };
	    }
	    selectNameType(name, designatorType, partId) {
	        const useId = partId - 1;
	        switch (designatorType) {
	            case NameGeneratorDesignatorType.Logical:
	                if (name.logicalMultiPart.length > 0) {
	                    if (useId >= name.logicalMultiPart.length) {
	                        throw new Error(`Part ID ${partId} is out of range for multi-part component ${name.logical} (${name.logicalMultiPart.join(', ')})`);
	                    }
	                    return name.logicalMultiPart[useId];
	                }
	                return name.logical;
	            case NameGeneratorDesignatorType.Physical:
	                return name.physical;
	            case NameGeneratorDesignatorType.PhysicalInSchematic:
	                if (name.physicalInSchematicMultiPart.length > 0) {
	                    if (useId >= name.physicalInSchematicMultiPart.length) {
	                        throw new Error(`Part ID ${partId} is out of range for multi-part component ${name.physicalInSchematic} (${name.physicalInSchematicMultiPart.join(', ')})`);
	                    }
	                    return name.physicalInSchematicMultiPart[useId];
	                }
	                return name.physicalInSchematic;
	        }
	    }
	    generatePowerPortName(powerPort) {
	        const paddedIndex = powerPort.index.toString().padStart(3, '0');
	        return `#PWR_${powerPort.text}_${paddedIndex}`;
	    }
	    canUseComponentPhysicalName(forModuleRef, name, nameType) {
	        if (!this.physicalNameToRefMap.has(name)) {
	            return true;
	        }
	        let useValue;
	        switch (nameType) {
	            case NameGeneratorDesignatorType.Logical:
	                useValue = this.physicalNameToRefMap.get(name)?.physical;
	                break;
	            case NameGeneratorDesignatorType.Physical:
	                useValue = this.physicalNameToRefMap.get(name)?.physical;
	                break;
	            case NameGeneratorDesignatorType.PhysicalInSchematic:
	                useValue = this.physicalNameToRefMap.get(name)?.physicalInSchematic;
	                break;
	        }
	        return useValue === forModuleRef;
	    }
	    componentRefHasName(ref) {
	        return this.refToNameMap.has(ref);
	    }
	    getComponentNameFromRef(ref) {
	        return this.refToNameMap.get(ref);
	    }
	    getComponentRefFromName(name) {
	        return this.physicalNameToRefMap.get(name);
	    }
	    registerComponentName(ref, inputName, partCount, sourceDocument) {
	        const name = {
	            ...inputName,
	            logicalMultiPart: this.generateMultiPartNames(inputName.logical, partCount),
	            physicalInSchematicMultiPart: this.generateMultiPartNames(inputName.physicalInSchematic, partCount),
	        };
	        let existing = this.refToNameMap.get(ref);
	        if (existing) {
	            if (_.isEqual(existing, name)) {
	                return name;
	            }
	            throw new Error(`Component ref ${ref} already has a name: ${this.refToNameMap.get(ref)?.physical}`);
	        }
	        if (name.isPhysicalPart) {
	            existing = this.physicalNameToRefMap.get(name.physical);
	            if (existing) {
	                if (existing.logicalMultiPart.length > 0 && _.isEqual(existing, name)) {
	                    return name;
	                }
	                this.recordProcessingResult({
	                    type: 'altium-name-generator',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: `Duplicate component designator ${name.physical}${sourceDocument ? ` in ${sourceDocument}` : ''}`,
	                });
	            }
	            else {
	                this.physicalNameToRefMap.set(name.physical, name);
	            }
	        }
	        this.refToNameMap.set(ref, name);
	        return name;
	    }
	    recordProcessingResult(result) {
	        this.processingResults.push(result);
	        this.processingResultSink?.(result);
	    }
	    generateMultiPartNames(logical, partCount) {
	        if (partCount <= 1) {
	            return [];
	        }
	        const names = [];
	        for (let i = 0; i < partCount; i++) {
	            names.push(`${logical}${String.fromCharCode('A'.charCodeAt(0) + i)}`);
	        }
	        return names;
	    }
	    annotateReferenceDesignatorUsingChannelDesignatorFormatString(component, args) {
	        const originalComponent = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(args, 'Component') ?? component;
	        const diffPairSuffix = this.extractDifferentialPairSuffix(originalComponent);
	        const componentWithoutDiffPairSuffix = diffPairSuffix ?
	            originalComponent.substring(0, originalComponent.length - diffPairSuffix.length)
	            : originalComponent;
	        const myArgs = {
	            ComponentPrefix: () => /^([^0-9_-]+)/.exec(componentWithoutDiffPairSuffix)?.[1],
	            ComponentIndex: () => /([0-9]+)[A-Za-z]*$/.exec(componentWithoutDiffPairSuffix)?.[1],
	            ...args,
	            Component: componentWithoutDiffPairSuffix,
	        };
	        const formatString = this.projectConfigUtil.getDesignConfiguration('ChannelDesignatorFormatString');
	        const replaceFn = (key) => {
	            return this.resolveOrError(myArgs, key);
	        };
	        return (formatString
	            .replace(/\$RoomName/g, replaceFn)
	            .replace(/\$ChannelAlpha/g, replaceFn)
	            .replace(/\$ChannelPrefix/g, replaceFn)
	            .replace(/\$ChannelIndex/g, replaceFn)
	            .replace(/\$ComponentPrefix/g, replaceFn)
	            .replace(/\$ComponentIndex/g, replaceFn)
	            .replace(/\$Component/g, replaceFn) + diffPairSuffix);
	    }
	    extractDifferentialPairSuffix(netName) {
	        const suffix = this.projectConfigUtil
	            .getDifferentialPairSuffixes()
	            .find((candidate) => candidate.startsWith('_') && netName.toLowerCase().endsWith(candidate.toLowerCase()));
	        return suffix === undefined ? '' : netName.slice(-suffix.length);
	    }
	    resolveOrError(args, key) {
	        if (key.startsWith('$')) {
	            key = key.substring(1);
	        }
	        return key in args ?
	            (sigma_engine_shared_2.Utils.resolveValueFromThunkMap(args, key) ?? `{ERROR:${key}NotProvided}`)?.toString()
	            : `{ERROR:${key}NotProvided}`;
	    }
	}
	AltiumNameGenerator.AltiumNameGenerator = AltiumNameGenerator$1;
	
	return AltiumNameGenerator;
}

var AltiumSpecialStringResolver = {};

var hasRequiredAltiumSpecialStringResolver;

function requireAltiumSpecialStringResolver () {
	if (hasRequiredAltiumSpecialStringResolver) return AltiumSpecialStringResolver;
	hasRequiredAltiumSpecialStringResolver = 1;
	Object.defineProperty(AltiumSpecialStringResolver, "__esModule", { value: true });
	AltiumSpecialStringResolver.AltiumSpecialStringResolver = AltiumSpecialStringResolver.ExpressionTermKind = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	var ExpressionTermKind;
	(function (ExpressionTermKind) {
	    ExpressionTermKind["Literal"] = "literal";
	    ExpressionTermKind["Identifier"] = "identifier";
	})(ExpressionTermKind || (AltiumSpecialStringResolver.ExpressionTermKind = ExpressionTermKind = {}));
	class AltiumSpecialStringResolver$1 {
	    static { this.SPECIAL_STRING_PATTERN = /=([A-Za-z][A-Za-z0-9_]*)/g; }
	    constructor(context, parentOrParamResolver, paramResolver) {
	        this.context = context;
	        this.parent = parentOrParamResolver instanceof AltiumSpecialStringResolver$1 ? parentOrParamResolver : undefined;
	        this.paramResolver =
	            paramResolver ??
	                (parentOrParamResolver instanceof AltiumSpecialStringResolver$1 ? undefined : parentOrParamResolver);
	    }
	    createChildResolver(contextOverrides) {
	        return new AltiumSpecialStringResolver$1(contextOverrides, this);
	    }
	    containsSpecialString(text) {
	        return (AltiumSpecialStringResolver$1.extractWholeStringFormName(text) !== undefined ||
	            AltiumSpecialStringResolver$1.containsEmbeddedToken(text));
	    }
	    resolve(text) {
	        return this.resolveText(text, new Set());
	    }
	    resolveText(text, activeTexts) {
	        if (activeTexts.has(text)) {
	            return text;
	        }
	        activeTexts.add(text);
	        try {
	            const expressionTerms = AltiumSpecialStringResolver$1.parseConcatenationExpression(text);
	            if (expressionTerms !== undefined) {
	                return this.evaluateConcatenation(expressionTerms, activeTexts) ?? text;
	            }
	            const wholeStringName = AltiumSpecialStringResolver$1.extractWholeStringFormName(text);
	            if (wholeStringName !== undefined) {
	                const value = this.resolveVariable(wholeStringName);
	                if (value !== undefined) {
	                    return this.resolveText(value, activeTexts);
	                }
	                if (!AltiumSpecialStringResolver$1.containsEmbeddedToken(text.slice(1))) {
	                    return text;
	                }
	            }
	            AltiumSpecialStringResolver$1.SPECIAL_STRING_PATTERN.lastIndex = 0;
	            return text.replace(AltiumSpecialStringResolver$1.SPECIAL_STRING_PATTERN, (match, variableName) => {
	                const value = this.resolveVariable(variableName);
	                return value !== undefined ? this.resolveText(value, activeTexts) : match;
	            });
	        }
	        finally {
	            activeTexts.delete(text);
	        }
	    }
	    evaluateConcatenation(terms, activeTexts) {
	        let result = '';
	        for (const term of terms) {
	            if (term.kind === ExpressionTermKind.Literal) {
	                result += term.value;
	                continue;
	            }
	            const value = this.resolveVariable(term.value);
	            if (value === undefined) {
	                return undefined;
	            }
	            result += this.resolveText(value, activeTexts);
	        }
	        return result;
	    }
	    static parseConcatenationExpression(text) {
	        if (!text.startsWith('=') || /^=\s/.test(text)) {
	            return undefined;
	        }
	        const tokenPattern = /\s*(?:'([^']*)'|"([^"]*)"|([A-Za-z_][A-Za-z0-9_]*))\s*/y;
	        const terms = [];
	        let position = 1;
	        for (;;) {
	            tokenPattern.lastIndex = position;
	            const match = tokenPattern.exec(text);
	            if (match === null) {
	                return undefined;
	            }
	            const [, singleQuoted, doubleQuoted, identifier] = match;
	            terms.push(identifier !== undefined ?
	                { kind: ExpressionTermKind.Identifier, value: identifier }
	                : { kind: ExpressionTermKind.Literal, value: singleQuoted ?? doubleQuoted ?? '' });
	            position = tokenPattern.lastIndex;
	            if (position === text.length) {
	                break;
	            }
	            if (text[position] !== '+') {
	                return undefined;
	            }
	            position += 1;
	        }
	        return terms.length > 1 ? terms : undefined;
	    }
	    static containsEmbeddedToken(text) {
	        AltiumSpecialStringResolver$1.SPECIAL_STRING_PATTERN.lastIndex = 0;
	        return AltiumSpecialStringResolver$1.SPECIAL_STRING_PATTERN.test(text);
	    }
	    static extractWholeStringFormName(text) {
	        if (!text.startsWith('=')) {
	            return undefined;
	        }
	        const remainder = text.slice(1);
	        if (remainder === '' || remainder !== remainder.trimStart()) {
	            return undefined;
	        }
	        const trimmedRemainder = remainder.trimEnd();
	        const quoted = /^"(.*)"$/.exec(trimmedRemainder) ?? /^'(.*)'$/.exec(trimmedRemainder);
	        const variableName = quoted ? quoted[1] : trimmedRemainder;
	        if (variableName === '') {
	            return undefined;
	        }
	        return variableName;
	    }
	    resolveVariable(variableName) {
	        const normalizedName = variableName.toLowerCase();
	        let value = this.valueToString(this.findInContext(normalizedName, this.context));
	        if (!this.isEmpty(value)) {
	            return value;
	        }
	        value = this.paramResolver?.(variableName);
	        if (!this.isEmpty(value)) {
	            return value;
	        }
	        return this.parent?.resolveVariable(variableName);
	    }
	    isEmpty(value) {
	        return value === undefined || value === null || value === '' || value === '*';
	    }
	    findInContext(normalizedName, context) {
	        const contextKey = Object.keys(context).find((k) => k.toLowerCase() === normalizedName);
	        if (contextKey === undefined) {
	            return undefined;
	        }
	        const thunkValue = context[contextKey];
	        return sigma_engine_shared_1.Utils.resolveValueFromThunk(thunkValue);
	    }
	    valueToString(value) {
	        if (value === undefined) {
	            return undefined;
	        }
	        return String(value);
	    }
	}
	AltiumSpecialStringResolver.AltiumSpecialStringResolver = AltiumSpecialStringResolver$1;
	
	return AltiumSpecialStringResolver;
}

var AltiumLabelTypes = {};

var hasRequiredAltiumLabelTypes;

function requireAltiumLabelTypes () {
	if (hasRequiredAltiumLabelTypes) return AltiumLabelTypes;
	hasRequiredAltiumLabelTypes = 1;
	Object.defineProperty(AltiumLabelTypes, "__esModule", { value: true });
	AltiumLabelTypes.AltiumLabelTypeNameGeneratorSchema = AltiumLabelTypes.AltiumLabelTypesSchema = AltiumLabelTypes.AltiumCombobulationDimensions = void 0;
	AltiumLabelTypes.nameAltiumBusDimension = nameAltiumBusDimension;
	const zod_1 = /*@__PURE__*/ requireZod();
	var AltiumCombobulationDimensions;
	(function (AltiumCombobulationDimensions) {
	    AltiumCombobulationDimensions["Wire"] = "wire";
	    AltiumCombobulationDimensions["Bus"] = "bus";
	    AltiumCombobulationDimensions["SignalHarness"] = "altium-harness";
	})(AltiumCombobulationDimensions || (AltiumLabelTypes.AltiumCombobulationDimensions = AltiumCombobulationDimensions = {}));
	function nameAltiumBusDimension(busName, busMemberName) {
	    return `${busName}:${busMemberName}`;
	}
	AltiumLabelTypes.AltiumLabelTypesSchema = zod_1.default.enum([
	    'net-label',
	    'power-port',
	    'off-sheet-connector',
	    'port',
	    'sheet-entry',
	]);
	AltiumLabelTypes.AltiumLabelTypeNameGeneratorSchema = zod_1.default.enum([
	    'net-label',
	    'power-port',
	    'off-sheet-connector',
	    'port',
	    'sheet-entry',
	    'ComponentConnection',
	    'NamingPin',
	]);
	
	return AltiumLabelTypes;
}

var hasRequiredAltiumSchematicTransformer;

function requireAltiumSchematicTransformer () {
	if (hasRequiredAltiumSchematicTransformer) return AltiumSchematicTransformer;
	hasRequiredAltiumSchematicTransformer = 1;
	Object.defineProperty(AltiumSchematicTransformer, "__esModule", { value: true });
	AltiumSchematicTransformer.AltiumSchematicTransformer = void 0;
	AltiumSchematicTransformer.getNoConnectVirtualNetMetadata = getNoConnectVirtualNetMetadata;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumBusRange_1 = requireAltiumBusRange();
	const SchDocTypes_1 = requireSchDocTypes();
	const AltiumShapeConverters_1 = requireAltiumShapeConverters();
	const AltiumCombobulatorConfig_1 = requireAltiumCombobulatorConfig();
	const fflate_1 = /*@__PURE__*/ requireBrowser();
	const Area_1 = requireArea();
	const project_1 = requireProject();
	const AltiumNameGenerator_1 = requireAltiumNameGenerator();
	const AltiumSpecialStringResolver_1 = requireAltiumSpecialStringResolver();
	const layers_1 = requireLayers();
	const AltiumEnums_1 = requireAltiumEnums();
	const AltiumLabelTypes_1 = requireAltiumLabelTypes();
	const NetCombobulator_1 = requireNetCombobulator();
	const CoordinateConverter_1 = requireCoordinateConverter();
	const fonts_1 = requireFonts();
	const PathUtils_1 = requirePathUtils();
	const PropertyParser_1 = requirePropertyParser();
	const SNAPPED_PIN_CONTACT_NOISE_TOLERANCE_MILS = 0.01;
	const JUMPER_MODELLED_COMPONENT_KINDS = new Set([
	    AltiumEnums_1.ComponentKind.NetTieInBOM,
	    AltiumEnums_1.ComponentKind.NetTieNoBOM,
	    AltiumEnums_1.ComponentKind.Jumper,
	]);
	function getNoConnectVirtualNetMetadata({ connections, wires, labels, connectionIsHiddenPin, }) {
	    if (connections === 1 && wires === 0 && labels === 0 && connectionIsHiddenPin) {
	        return { [sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY]: sigma_engine_shared_1.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE };
	    }
	    return undefined;
	}
	function isJumperModelledComponentKind(component) {
	    const kind = component.record.componentkind;
	    return kind !== undefined && JUMPER_MODELLED_COMPONENT_KINDS.has(kind);
	}
	class AltiumSchematicTransformer$1 {
	    constructor(schematic, parsedDef, projectConfigUtil, nameGenerator, instances, projectModel, instanceCombobulators, fontMapper, namingStrategy, usedSymbols) {
	        this.schematic = schematic;
	        this.parsedDef = parsedDef;
	        this.projectConfigUtil = projectConfigUtil;
	        this.nameGenerator = nameGenerator;
	        this.projectModel = projectModel;
	        this.fontMapper = fontMapper;
	        this.namingStrategy = namingStrategy;
	        this.usedSymbols = usedSymbols;
	        this.populatedComponentSignalDisplayModes = new WeakMap();
	        this.pinNetHiddenPriorities = new Map();
	        this.snappedPinContactCandidates = [];
	        this.hiddenPinIsolationCounter = 0;
	        this.rangedBusDimensionsByAnchor = new Map();
	        this.enableDebugOutputs = false;
	        this.extractedImages = [];
	        this.instances = [];
	        this.instanceCombobulators = new Map();
	        this.addInstances(instances, instanceCombobulators);
	        this.processingResults = {
	            type: 'altium-schematic-transformer',
	            schematic: this.schematic.fileName,
	            message: '',
	            innerResults: [],
	        };
	        this.specialStringResolver = this.createSpecialStringResolver();
	    }
	    createSpecialStringResolver() {
	        const context = {
	            ProjectName: () => this.projectConfigUtil.projectFileName ?? this.parsedDef.name ?? this.extractProjectName(),
	            SheetNumber: () => this.instances[0]?.hierarchyEntry.sheetNumber?.toString() ??
	                this.instances[0]?.hierarchyEntry.schematicIndex?.toString(),
	            SheetTotal: () => this.projectConfigUtil.sheetCount.toString(),
	            DocumentName: () => this.resolveParameterValue('DocumentName', false) ?? this.extractSheetName(true),
	            CurrentDate: () => new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }),
	        };
	        return new AltiumSpecialStringResolver_1.AltiumSpecialStringResolver(context, (key) => this.resolveParameterValue(key));
	    }
	    resolveParameterValue(key, enableProjectLookup = true) {
	        const normalizedKey = key.toLowerCase();
	        const schematicParams = this.schematic
	            .getChildRecords(-1)
	            .filter((r) => r.recordType === SchDocTypes_1.RecordType.PARAMETER)
	            .filter((p) => p.name.toLowerCase() === normalizedKey);
	        if (schematicParams.length > 0) {
	            const nonEmptyParams = schematicParams.filter((p) => !['', '*'].includes(p.text.trim()));
	            if (nonEmptyParams.length > 1) {
	                this.processingResults.innerResults.push({
	                    type: 'warning',
	                    message: `Multiple non-empty values for parameter ${key}. Using first match.`,
	                });
	            }
	            if (nonEmptyParams.length > 0) {
	                return nonEmptyParams[0].text;
	            }
	        }
	        if (enableProjectLookup) {
	            return this.projectConfigUtil.getProjectParameter(key);
	        }
	        return undefined;
	    }
	    extractProjectName() {
	        const schematicFiles = Object.keys(this.parsedDef.schematics);
	        if (schematicFiles.length > 0) {
	            const fileName = schematicFiles[0].split('/').pop() ?? schematicFiles[0];
	            return fileName.replace(/\.[^.]+$/, '');
	        }
	        return 'Unknown Project';
	    }
	    get model() {
	        if (!this.schematicModel) {
	            throw new Error('Schematic model not initialized');
	        }
	        return this.schematicModel;
	    }
	    getFinalModel() {
	        if (!this.instances) {
	            throw new Error('Instances not initialized');
	        }
	        if (!this.schematicModel) {
	            throw new Error('Schematic model not initialized');
	        }
	        if (this.instanceCombobulators.size !== this.instances.length) {
	            throw new Error('Not all instance nets have been generated');
	        }
	        return this.schematicModel;
	    }
	    getProjectFontId(altiumFontId) {
	        if (altiumFontId === 0 || altiumFontId === '0') {
	            return undefined;
	        }
	        const fontIdNum = typeof altiumFontId === 'string' ? parseInt(altiumFontId, 10) : altiumFontId;
	        const localFontMap = this.fontMapper.schematicFontMap.get(this.schematic.fileName);
	        if (!localFontMap) {
	            return undefined;
	        }
	        return localFontMap.get(fontIdNum);
	    }
	    isElectricallyEligible(component) {
	        return component.record.componentkind !== AltiumEnums_1.ComponentKind.Graphical;
	    }
	    addInstances(instances, instanceCombobulators) {
	        for (const instance of instances) {
	            if (instance.hierarchyEntry.schematic !== this.schematic) {
	                throw new Error('All instances must belong to the same schematic');
	            }
	        }
	        const multiInstanceInstance = this.multiInstanceInstance;
	        this.instances = [
	            ...this.instances.filter((instance) => instance !== multiInstanceInstance),
	            ...instances,
	            ...(multiInstanceInstance ? [multiInstanceInstance] : []),
	        ];
	        for (const [instance, combobulator] of instanceCombobulators) {
	            this.instanceCombobulators.set(instance, combobulator);
	        }
	        if (!multiInstanceInstance) {
	            this.createMultiInstanceInstance();
	        }
	    }
	    createMultiInstanceInstance() {
	        if (!this.instances.some((instance) => instance.isMultiChannel)) {
	            return;
	        }
	        const firstInstance = this.instances[0];
	        if (!firstInstance) {
	            throw new Error('No first instance found');
	        }
	        this.multiInstanceInstance = {
	            instanceId: `${firstInstance.hierarchyEntry.name}:MULTI-INSTANCE`,
	            pathUniqueId: (0, PathUtils_1.joinPathUniqueIds)(firstInstance.pathUniqueId.substring(0, firstInstance.pathUniqueId.lastIndexOf('\\')), `${firstInstance.hierarchyEntry.name}<null>`),
	            channelIndex: 0,
	            roomName: firstInstance.roomName,
	            channelPrefix: firstInstance.channelPrefix,
	            numInstances: 1,
	            totalSheetInstances: firstInstance.totalSheetInstances,
	            designWideInstances: firstInstance.designWideInstances,
	            instanceIndex: 0,
	            isMultiChannel: false,
	            isMultiChannelSchematicInstance: true,
	            hierarchyEntry: firstInstance.hierarchyEntry,
	            path: Array.from(firstInstance.path).splice(0, firstInstance.path.length - 1),
	            children: [],
	        };
	        this.instanceCombobulators.set(this.multiInstanceInstance, new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this.namingStrategy, this.multiInstanceInstance, {
	            pointRoundingPrecision: AltiumCombobulatorConfig_1.ALTIUM_POINT_ROUNDING_PRECISION,
	            connectionTolerance: AltiumCombobulatorConfig_1.ALTIUM_CONNECTION_TOLERANCE,
	        }));
	        this.instances.push(this.multiInstanceInstance);
	    }
	    prepareModuleAnnotationClaims() {
	        for (const instance of this.instances) {
	            if (instance.isMultiChannelSchematicInstance) {
	                continue;
	            }
	            for (const component of this.schematic
	                .getComponents()
	                .filter((candidate) => this.isElectricallyEligible(candidate))) {
	                const { sheetDescriptor, nameGenVars } = this.getModuleNameGeneratorArgs(instance, component);
	                this.nameGenerator.getAnnotationEntryForPart(sheetDescriptor, { ...nameGenVars, Component: this.getComponentDesignator(component) }, true);
	            }
	        }
	    }
	    initializeModuleNames() {
	        for (const instance of this.instances) {
	            for (const component of this.schematic.getComponents().filter((c) => this.isElectricallyEligible(c))) {
	                if (instance.isMultiChannelSchematicInstance) {
	                    return null;
	                }
	                this.computeComponentModuleNames(instance, component, true);
	            }
	        }
	    }
	    initializeSchematicModel() {
	        if (this.schematicModel) {
	            throw new Error('Schematic model already initialized');
	        }
	        if (this.instances.length === 0) {
	            throw new Error('Invariant failed: No instances found. There should always be at least one.');
	        }
	        this.pinNetHiddenPriorities.clear();
	        this.hiddenPinIsolationCounter = 0;
	        this.snappedPinContactCandidates.length = 0;
	        const sheetIndex = this.instances[0].hierarchyEntry.schematicIndex;
	        this.schematicModel = {
	            modelType: 'schematic',
	            designId: this.schematic.fileName,
	            designName: this.extractSheetName(),
	            sourceFile: this.schematic.fileName,
	            sheetIndex,
	            layerSet: layers_1.ALTIUM_SCH_LAYERSET,
	            drawings: Object.fromEntries((0, layers_1.getAltiumSchLayers)().map((layer) => [layer.id, []])),
	            modules: [],
	            nets: {},
	            ports: {},
	            warnings: [],
	            font: {
	                default: fonts_1.Font.Newstroke,
	            },
	            grid: {
	                distance: 1.27,
	                units: 'mil',
	                invertY: false,
	            },
	            edges_bbox: {
	                minx: Number.POSITIVE_INFINITY,
	                miny: Number.POSITIVE_INFINITY,
	                maxx: Number.NEGATIVE_INFINITY,
	                maxy: Number.NEGATIVE_INFINITY,
	            },
	            initial_zoombox: {
	                minx: Number.POSITIVE_INFINITY,
	                miny: Number.POSITIVE_INFINITY,
	                maxx: Number.NEGATIVE_INFINITY,
	                maxy: Number.NEGATIVE_INFINITY,
	            },
	        };
	        if (this.instances[0].numInstances > 1) {
	            this.schematicModel.multiInstanceConfiguration = {
	                instances: this.instances
	                    .filter((instance) => !instance.isMultiChannelSchematicInstance)
	                    .map((instance) => ({
	                    instanceSheetIndex: instance.hierarchyEntry.schematicIndex,
	                    instanceName: `[${sheetIndex + 1}] ${instance.roomName}`,
	                    instanceId: instance.pathUniqueId,
	                })),
	                multiInstanceModules: [],
	            };
	        }
	        this.extractModules(this.schematicModel);
	        this.extractPowerPorts(this.nameGenerator, this.schematicModel);
	        this.extractAndCombobulateWiresAndJunctions(this.schematicModel);
	        this.extractBuses(this.schematicModel);
	        this.markBusRangeTaps();
	        this.extractSignalHarnesses(this.schematicModel);
	        this.extractHarnessConnectors();
	        this.extractHarnessConnectorDrawings(this.schematicModel);
	        this.extractGraphics(this.schematicModel);
	        this.extractImages(this.schematicModel);
	        this.extractTextAndLabels(this.schematicModel);
	        this.extractSheetSymbols(this.schematicModel);
	        this.extractPorts(this.schematicModel);
	        this.applySnappedPinContactFallbacks();
	        this.calculateBoundingBoxes(this.schematicModel);
	        if (this.projectModel.designs[this.schematicModel.designId]) {
	            throw new Error(`Design ${this.schematicModel.designId} already exists in project model`);
	        }
	        this.projectModel.designs[this.schematicModel.designId] = this.schematicModel;
	        this.projectModel.processingResults.innerResults.push(this.processingResults);
	    }
	    extractSheetName(withExtension = false) {
	        const filename = this.schematic.fileName.split('/').pop() ?? this.schematic.fileName;
	        if (withExtension) {
	            return filename;
	        }
	        return filename.replace(/\.SchDoc$/i, '');
	    }
	    extractModules(schematicModel) {
	        const components = this.schematic.getComponents().filter((c) => this.isElectricallyEligible(c));
	        for (const component of components) {
	            this.reportUnrecognizedComponentKind(component);
	            const modules = this.createDesignModuleFromAltiumComponent(component);
	            schematicModel.modules.push(...modules);
	        }
	    }
	    reportUnrecognizedComponentKind(component) {
	        const unrecognizedKind = component.record.unrecognizedComponentKind;
	        if (unrecognizedKind === undefined) {
	            return;
	        }
	        this.processingResults.innerResults.push({
	            type: 'warning',
	            message: `Component ${this.getComponentDesignator(component)} declares unknown ComponentKind ${unrecognizedKind}; treating it as a standard part.`,
	            record: component.record,
	        });
	    }
	    createDesignModuleFromAltiumComponent(component) {
	        const sheet = this.schematic.getSheet();
	        const designator = this.getComponentDesignator(component);
	        const designatorRecord = component.getDesignator();
	        let name = designator;
	        if (component.record.partCount > 2) {
	            const partNo = component.record.currentPartId;
	            name += String.fromCharCode('A'.charCodeAt(0) + partNo - 1);
	        }
	        const componentRef = this.getComponentNameForAltiumComponent(component);
	        const { component: modelComponent, footprintRef } = this.createDesignModelComponentFromAltiumComponent(component);
	        const hiddenPinNetNames = new Map(component
	            .getPins()
	            .map((pin) => [pin.designator, component.getHiddenNetName(pin)])
	            .filter((entry) => entry[1] !== undefined));
	        const location = [
	            CoordinateConverter_1.CoordinateConverter.milsToMm(component.record.location.x),
	            CoordinateConverter_1.CoordinateConverter.milsToMm(component.record.location.y),
	        ];
	        let multiInstanceModule;
	        if (this.schematicModel.multiInstanceConfiguration) {
	            multiInstanceModule = {
	                ref: component.record.uniqueId,
	                name: name,
	                footprintRef,
	                componentRef,
	                center: location,
	                layer: 'F',
	                transform: {
	                    pos: [0, 0],
	                    angle: (component.record.orientation - 1) * -90,
	                    mirrored: [false, component.record.isMirrored],
	                },
	                drawProperties: {},
	                pinNets: {},
	                metadata: {
	                    'altium.uniqueId': component.record.uniqueId,
	                    'altium.partCount': component.record.partCount.toString(),
	                    'altium.displayMode': component.record.displayMode.toString(),
	                },
	            };
	            if (designatorRecord) {
	                multiInstanceModule.drawProperties['designator'] = {
	                    hidden: false,
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(`${designatorRecord.text}`),
	                    angle: designatorRecord.orientation * 90,
	                    justify: CoordinateConverter_1.CoordinateConverter.convertJustification(designatorRecord.justification),
	                    pos: [
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.x),
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.y),
	                    ],
	                    mirror: [false, false],
	                    layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.DESIGNATOR).id,
	                    ...sheet.getFont(designatorRecord.fontId).textOpts,
	                };
	            }
	            if (this.multiInstanceInstance) {
	                this.combobulateModule(this.multiInstanceInstance, modelComponent, multiInstanceModule, [], hiddenPinNetNames);
	            }
	            this.schematicModel.multiInstanceConfiguration.multiInstanceModules.push(multiInstanceModule);
	        }
	        return this.instances
	            .map((instance) => {
	            if (instance.isMultiChannelSchematicInstance) {
	                return null;
	            }
	            const pathUniqueId = (0, PathUtils_1.joinPathUniqueIds)(instance.pathUniqueId, component.record.uniqueId);
	            const { instanceName, instanceFullName, nameSuffix } = this.computeComponentModuleNames(instance, component, false);
	            const module = {
	                ref: `${pathUniqueId}${nameSuffix ?? ''}`,
	                name: instanceName,
	                fullModuleName: instanceFullName,
	                footprintRef,
	                componentRef,
	                center: location,
	                layer: 'F',
	                transform: {
	                    pos: [0, 0],
	                    angle: (component.record.orientation - 1) * -90,
	                    mirrored: [false, component.record.isMirrored],
	                },
	                pinNets: {},
	                drawProperties: {},
	                metadata: {
	                    'altium.uniqueId': component.record.uniqueId,
	                    'altium.partCount': component.record.partCount.toString(),
	                    'altium.displayMode': component.record.displayMode.toString(),
	                },
	                multiInstance: multiInstanceModule ?
	                    {
	                        instanceId: instance.pathUniqueId,
	                        displayModuleRef: multiInstanceModule.ref,
	                    }
	                    : undefined,
	            };
	            const projectModuleName = instanceFullName ?? instanceName;
	            let projectModule = this.projectModel.modules.find((m) => m.name === projectModuleName);
	            if (!projectModule) {
	                projectModule = {
	                    name: projectModuleName,
	                    instances: [],
	                    componentRef: module.componentRef,
	                    metadata: {},
	                };
	                this.projectModel.modules.push(projectModule);
	            }
	            if (!projectModule.metadata) {
	                projectModule.metadata = {};
	            }
	            projectModule.instances.push({
	                ref: module.ref,
	                designId: this.schematicModel.designId,
	            });
	            const variant = this.projectConfigUtil.getPartVariationForUniqueId(pathUniqueId);
	            const variantParamOverrides = variant ? this.projectConfigUtil.getParamVariationsForVariation(variant) : new Map();
	            if (variant) {
	                projectModule.metadata['altium.variationKind'] = project_1.AltiumVariationKind[variant.Kind];
	                if (variant.Kind === project_1.AltiumVariationKind.NotFitted) {
	                    projectModule.isDnp = true;
	                }
	            }
	            for (const [column, kind] of this.projectConfigUtil.getUnappliedVariantColumnsForUniqueId(pathUniqueId)) {
	                projectModule.metadata[column] = kind;
	            }
	            if (designatorRecord) {
	                module.drawProperties['designator'] = {
	                    hidden: false,
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(instanceName),
	                    angle: designatorRecord.orientation * 90,
	                    justify: CoordinateConverter_1.CoordinateConverter.convertJustification(designatorRecord.justification),
	                    pos: [
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.x),
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(designatorRecord.location.y),
	                    ],
	                    mirror: [false, false],
	                    layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.DESIGNATOR).id,
	                    ...sheet.getFont(designatorRecord.fontId).textOpts,
	                };
	            }
	            const parameters = component.getParameters();
	            const componentParamContext = {};
	            for (const param of parameters) {
	                if (param.name && param.text) {
	                    componentParamContext[param.name] = param.text;
	                }
	            }
	            for (const [overrideName, overrideValue] of variantParamOverrides) {
	                componentParamContext[overrideName] = overrideValue;
	            }
	            const componentResolver = this.specialStringResolver.createChildResolver(componentParamContext);
	            if (variant?.Kind === project_1.AltiumVariationKind.AlternatePart) {
	                projectModule.metadata['altium.alternatePart'] = componentResolver.resolve(variant.AlternatePart);
	                const alternateDesignItemId = variant.AltLibLink_DesignItemID?.trim();
	                if (alternateDesignItemId) {
	                    projectModule.metadata['altium.alternateDesignItemId'] = alternateDesignItemId;
	                }
	            }
	            const appliedParamNames = new Set();
	            for (const param of parameters) {
	                if (param.name && (param.text || variantParamOverrides.has(param.name))) {
	                    appliedParamNames.add(param.name);
	                    const resolvedValue = componentResolver.resolve(variantParamOverrides.get(param.name) ?? param.text);
	                    module.metadata[param.name] = resolvedValue;
	                    projectModule.metadata[param.name] = resolvedValue;
	                    const drawProp = {
	                        hidden: param.isHidden,
	                        ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(resolvedValue),
	                        angle: param.orientation * 90,
	                        val: ['comment', 'value'].includes(param.name.toLowerCase()),
	                        justify: CoordinateConverter_1.CoordinateConverter.convertJustification(param.justification),
	                        pos: [CoordinateConverter_1.CoordinateConverter.milsToMm(param.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(param.location.y)],
	                        mirror: [
	                            false,
	                            component.record.isMirrored !== param.isMirrored && (component.record.isMirrored || param.isMirrored),
	                        ],
	                        layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.PARAMETER).id,
	                        ...sheet.getFont(param.fontId).textOpts,
	                    };
	                    module.drawProperties[param.name] = drawProp;
	                    if (multiInstanceModule) {
	                        multiInstanceModule.drawProperties[param.name] = {
	                            ...drawProp,
	                            hidden: true,
	                        };
	                    }
	                }
	            }
	            for (const [overrideName, overrideValue] of variantParamOverrides) {
	                if (appliedParamNames.has(overrideName)) {
	                    continue;
	                }
	                const resolvedValue = componentResolver.resolve(overrideValue);
	                module.metadata[overrideName] = resolvedValue;
	                projectModule.metadata[overrideName] = resolvedValue;
	            }
	            if (!projectModule.value) {
	                projectModule.value = Array.from(Object.values(module.drawProperties)).find((p) => p.val && p.value.trim())?.value;
	            }
	            const implementationLists = component.getImplementationLists();
	            for (const implementationList of implementationLists) {
	                for (const implementation of implementationList.getImplementations()) {
	                    if (implementation.modelType === 'PCBLIB') {
	                        module.metadata['Footprint'] = implementation.modelName;
	                        module.metadata['FootprintDescription'] = implementation.description;
	                    }
	                }
	            }
	            if (projectModule.name.startsWith('*') &&
	                !modelComponent.symbols.find((s) => s.ref === module.footprintRef)?.pins?.length) {
	                projectModule.hideInBom = true;
	            }
	            this.combobulateModule(instance, modelComponent, module, [], hiddenPinNetNames);
	            return module;
	        })
	            .filter((m) => m !== null);
	    }
	    getComponentDesignator(component) {
	        const designatorRecord = component.getDesignator();
	        let designator = designatorRecord?.text ?? `Component_${component.record.index}`;
	        if (designator === '*') {
	            designator = component.record.uniqueId;
	        }
	        return designator;
	    }
	    computeComponentModuleNames(instance, component, explicitOnly) {
	        const designator = this.getComponentDesignator(component);
	        const nameAnnotationMode = AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOrChannel;
	        const nameSuffix = component.record.partCount > 2 ?
	            String.fromCharCode('A'.charCodeAt(0) + component.record.currentPartId - 1)
	            : undefined;
	        const { sheetDescriptor, nameGenVars } = this.getModuleNameGeneratorArgs(instance, component);
	        let instanceName;
	        let instanceFullName;
	        if (explicitOnly) {
	            instanceName = this.nameGenerator.getAnnotationsForModule(designator, sheetDescriptor, nameGenVars, AltiumNameGenerator_1.NameGeneratorDesignatorType.PhysicalInSchematic);
	            instanceFullName =
	                nameSuffix ?
	                    this.nameGenerator.getAnnotationsForModule(designator, sheetDescriptor, nameGenVars, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical)
	                    : undefined;
	        }
	        else {
	            instanceName = this.nameGenerator.generateName(designator, sheetDescriptor, nameGenVars, nameAnnotationMode, AltiumNameGenerator_1.NameGeneratorDesignatorType.PhysicalInSchematic, AltiumNameGenerator_1.NameGeneratorNameType.Module);
	            instanceFullName =
	                nameSuffix ?
	                    this.nameGenerator.generateName(designator, sheetDescriptor, nameGenVars, nameAnnotationMode, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical, AltiumNameGenerator_1.NameGeneratorNameType.Module)
	                    : undefined;
	        }
	        return { instanceName, instanceFullName, nameSuffix };
	    }
	    getModuleNameGeneratorArgs(instance, component) {
	        const physicalContextParent = instance.inheritsPhysicalNamingContext ? instance.path.at(-1) : undefined;
	        const sheetName = instance.inheritsPhysicalNamingContext ? (instance.physicalRoomName ?? instance.roomName) : instance.roomName;
	        const roomName = instance.inheritsPhysicalNamingContext ? (instance.physicalRoomName ?? instance.roomName) : instance.roomName;
	        const channelIndex = instance.inheritsPhysicalNamingContext ?
	            (instance.physicalChannelIndex ?? instance.channelIndex)
	            : instance.channelIndex;
	        const physicalChannelIndex = instance.physicalChannelIndex ?? instance.channelIndex;
	        const channelPrefix = instance.inheritsPhysicalNamingContext ?
	            (instance.physicalChannelPrefix ?? instance.channelPrefix)
	            : instance.channelPrefix;
	        const sheetDescriptor = {
	            name: sheetName,
	            filename: instance.hierarchyEntry.projectDocumentPath ?? instance.hierarchyEntry.sourceFile,
	            uniqueId: instance.sheetSymbol?.uniqueId ?? '',
	            pathUniqueId: instance.pathUniqueId,
	            roomName,
	            channelIndex,
	            physicalChannelIndex,
	            channelPrefix,
	            isMultiChannel: instance.isMultiChannel,
	            numInstances: instance.inheritsPhysicalNamingContext ? 1 : instance.numInstances,
	            totalSheetInstances: physicalContextParent?.totalSheetInstances ?? instance.totalSheetInstances,
	            designWideInstances: physicalContextParent ? physicalContextParent.designWideInstances : instance.designWideInstances,
	        };
	        const nameGenVars = {
	            UniqueID: component.record.uniqueId,
	            PathUniqueID: (0, PathUtils_1.joinPathUniqueIds)(instance.pathUniqueId ?? '', component.record.uniqueId),
	            PartCount: component.record.partCount - 1,
	            PartId: component.record.currentPartId,
	            IsPhysicalPart: (component.record.partCount > 1 && component.getImplementationLists().some((i) => i.hasImplementations())) ||
	                (component.record.allPinCount ?? 0) > 0,
	        };
	        return { sheetDescriptor, nameGenVars };
	    }
	    recordSnappedPinContactCandidate(instance, connection, netPosition, pinLengthMils) {
	        const sheet = this.schematic.tryGetSheet();
	        if (!sheet?.isSnapGridEnabled()) {
	            return;
	        }
	        const snapGridSizeMils = sheet.getSnapGridSizeMils();
	        const snappedPosition = AltiumShapeConverters_1.AltiumShapeConverters.snapPositionToGrid(netPosition, snapGridSizeMils);
	        if (!snappedPosition) {
	            return;
	        }
	        const gridResidualMils = Math.abs(pinLengthMils - Math.round(pinLengthMils / snapGridSizeMils) * snapGridSizeMils);
	        const maxAxisDisplacementMils = gridResidualMils + SNAPPED_PIN_CONTACT_NOISE_TOLERANCE_MILS;
	        const exceedsAllowance = [0, 1].some((axis) => Math.abs(CoordinateConverter_1.CoordinateConverter.mmToMils(snappedPosition[axis] - netPosition[axis])) > maxAxisDisplacementMils);
	        if (exceedsAllowance) {
	            return;
	        }
	        this.snappedPinContactCandidates.push({
	            instance,
	            moduleRef: connection.moduleRef,
	            pinRef: connection.pinRef,
	            literalPosition: netPosition,
	            snappedPosition,
	        });
	    }
	    applySnappedPinContactFallbacks() {
	        for (const candidate of this.snappedPinContactCandidates) {
	            const combobulator = this.getInstanceCombobulator(candidate.instance);
	            const literalContacts = combobulator
	                .getObjsAtPosition(candidate.literalPosition, combobulator.defaultDimension.name)
	                .filter((object) => {
	                const pinConnection = object.connection?.connection;
	                return pinConnection?.moduleRef !== candidate.moduleRef || pinConnection.pinRef !== candidate.pinRef;
	            });
	            if (literalContacts.length > 0) {
	                continue;
	            }
	            const snappedContacts = combobulator.getObjsAtPosition(candidate.snappedPosition, combobulator.defaultDimension.name);
	            if (snappedContacts.length === 0) {
	                continue;
	            }
	            combobulator.addWire({ from: candidate.literalPosition, to: candidate.snappedPosition });
	        }
	    }
	    combobulateModule(instance, modelComponent, module, labels = [], hiddenPinNetNames = new Map()) {
	        const combobulator = this.getInstanceCombobulator(instance);
	        const symbol = modelComponent.symbols.find((s) => s.ref === module.footprintRef);
	        if (!symbol) {
	            throw new Error(`Symbol ${module.footprintRef} not found for component ${modelComponent.name}`);
	        }
	        const pos = module.center;
	        const angle = module.transform.angle ?? 0;
	        const mirrored = module.transform.mirrored ?? [false, false];
	        for (const pin of symbol.pins) {
	            const netPosition = sigma_engine_shared_1.Utils.Transform.translatePoint(pos, sigma_engine_shared_1.Utils.Transform.getXyWithMirror(sigma_engine_shared_1.Utils.Transform.rotatePoint(pin.start, (mirrored[1] ? 1 : -1) * angle), mirrored));
	            if (this.enableDebugOutputs) {
	                const layer = (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Other).id;
	                if (!this.schematicModel.drawings[layer]) {
	                    this.schematicModel.drawings[layer] = [];
	                }
	                this.schematicModel.drawings[layer].push({
	                    type: 'junction',
	                    position: netPosition,
	                });
	                this.schematicModel.drawings[layer].push({
	                    type: 'text',
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(pin.name),
	                    pos: sigma_engine_shared_1.Utils.Transform.translatePoint(netPosition, [0.5, 0]),
	                    angle: 0,
	                    justify: [-1, -1],
	                    size: 1,
	                    thickness: 1,
	                });
	            }
	            const connection = {
	                moduleRef: module.ref,
	                pinRef: pin.ref,
	                moduleName: module.fullModuleName ?? module.name,
	                ...(pin.hidden ? { hiddenPin: true } : {}),
	            };
	            const connectionPosition = pin.hidden ? [Number.MIN_SAFE_INTEGER, this.hiddenPinIsolationCounter++] : netPosition;
	            const netRef = combobulator.addConnection(connectionPosition, connection);
	            this.setModulePinNet(module, pin.ref, netRef.strCast(), connection.hiddenPin === true);
	            if (!pin.hidden) {
	                this.recordSnappedPinContactCandidate(instance, connection, netPosition, Math.hypot(CoordinateConverter_1.CoordinateConverter.mmToMils(pin.end[0] - pin.start[0]), CoordinateConverter_1.CoordinateConverter.mmToMils(pin.end[1] - pin.start[1])));
	            }
	            const hiddenPinBindingName = pin.hidden ? hiddenPinNetNames.get(pin.ref) : undefined;
	            if (hiddenPinBindingName) {
	                this.setCombobulatorLabel(instance, connectionPosition, hiddenPinBindingName, 'net-label', {
	                    hiddenNetName: hiddenPinBindingName,
	                });
	            }
	            const label = labels.find((l) => l.pinRef === pin.ref);
	            if (label) {
	                this.setCombobulatorLabel(instance, netPosition, label.value, label.type, label.metadata);
	            }
	        }
	    }
	    setModulePinNet(module, pinRef, netName, isHiddenPin) {
	        const priorityKey = `${module.ref}\0${pinRef}`;
	        const currentAssignmentIsHiddenPin = this.pinNetHiddenPriorities.get(priorityKey);
	        if (currentAssignmentIsHiddenPin !== false || !isHiddenPin) {
	            module.pinNets[pinRef] = netName;
	            this.pinNetHiddenPriorities.set(priorityKey, isHiddenPin);
	        }
	    }
	    isHiddenPinConnection(connection) {
	        return connection.hiddenPin === true;
	    }
	    toPublicNetConnection(connection) {
	        const { appliesNetName: _appliesNetName, hiddenPin: _hiddenPin, ...publicConnection } = connection;
	        return publicConnection;
	    }
	    createDesignModelComponentFromAltiumComponent(component) {
	        const componentName = this.getComponentNameForAltiumComponent(component);
	        const projectComponent = this.projectModel.components[componentName] ??
	            this.setComponentModelInformation(component, {
	                name: this.getComponentNameForAltiumComponent(component),
	                numPins: component.record.allPinCount ?? 0,
	                numPads: 0,
	                footprints: [],
	                symbols: [],
	                signals: [],
	                isVirtual: component.record.partCount === 1,
	                isMultiUnit: component.record.partCount > 1,
	                metadata: {
	                    Description: component.record.componentDescription,
	                    DesignItemId: component.record.designItemId,
	                    Library: component.record.sourceLibraryName,
	                },
	            });
	        const allComponentPins = component.getPins();
	        const allComponentGraphics = component.getGraphics();
	        const packagePartIds = component.record.partCount > 2 ?
	            Array.from(new Set([...allComponentPins, ...allComponentGraphics]
	                .map((record) => record.ownerPartId ?? -1)
	                .filter((ownerPartId) => ownerPartId > 0)))
	            : [component.record.currentPartId];
	        const sourcePartIds = Array.from(new Set([
	            component.record.currentPartId,
	            ...packagePartIds.filter((partId) => {
	                const partPrefix = `Part${String.fromCharCode('A'.charCodeAt(0) + partId - 1)}::`;
	                return !projectComponent.symbols.some((symbol) => symbol.ref.startsWith(partPrefix));
	            }),
	        ]));
	        let footprintRef = this.getSymbolNameForAltiumComponent(component);
	        for (const sourcePartId of sourcePartIds) {
	            const symbolName = this.getSymbolNameForAltiumComponent(component, sourcePartId);
	            const candidate = this.buildPartSymbol(component, sourcePartId, symbolName);
	            const existingSymbol = projectComponent.symbols.find((symbol) => symbol.ref === symbolName);
	            if (sourcePartId === component.record.currentPartId) {
	                if (!existingSymbol) {
	                    projectComponent.symbols.push(this.materializePartSymbolDrawings(component, sourcePartId, candidate, false));
	                    this.unionPartSignalsWhenModeAlreadyPopulated(projectComponent, component, sourcePartId);
	                }
	                else if (!this.usedSymbols.has(existingSymbol)) {
	                    const existingIndex = projectComponent.symbols.indexOf(existingSymbol);
	                    const storedCandidate = this.materializePartSymbolDrawings(component, sourcePartId, candidate, true);
	                    projectComponent.symbols.splice(existingIndex, 1, storedCandidate);
	                    this.reconcileSignalsForReplacedSymbol(projectComponent, existingSymbol, storedCandidate, component, sourcePartId);
	                }
	                else if (!this.symbolPinsMatch(existingSymbol, candidate)) {
	                    let variant = projectComponent.symbols.find((symbol) => symbol.ref.startsWith(`${symbolName}::Variant`) && this.symbolPinsMatch(symbol, candidate));
	                    if (!variant) {
	                        const variantName = this.getAvailableVariantSymbolName(projectComponent, symbolName, candidate);
	                        variant = this.materializePartSymbolDrawings(component, sourcePartId, {
	                            ...candidate,
	                            ref: variantName,
	                            name: variantName,
	                        }, true);
	                        projectComponent.symbols.push(variant);
	                    }
	                    footprintRef = variant.ref;
	                    this.unionComponentSignals(projectComponent, this.getPartPins(component, sourcePartId), component.record.displayMode);
	                }
	            }
	            else if (!existingSymbol) {
	                projectComponent.symbols.push(this.materializePartSymbolDrawings(component, sourcePartId, candidate, false));
	                this.unionPartSignalsWhenModeAlreadyPopulated(projectComponent, component, sourcePartId);
	            }
	            const populatedDisplayModes = this.populatedComponentSignalDisplayModes.get(projectComponent) ?? new Set();
	            if (!populatedDisplayModes.has(component.record.displayMode)) {
	                this.unionComponentSignals(projectComponent, allComponentPins.filter((pin) => pin.ownerPartId === -1 || sourcePartIds.includes(pin.ownerPartId)), component.record.displayMode);
	                populatedDisplayModes.add(component.record.displayMode);
	                this.populatedComponentSignalDisplayModes.set(projectComponent, populatedDisplayModes);
	            }
	        }
	        const footprint = projectComponent.symbols.find((symbol) => symbol.ref === footprintRef);
	        if (!footprint) {
	            throw new Error(`Missing symbol ${footprintRef} for ${projectComponent.name}`);
	        }
	        this.usedSymbols.add(footprint);
	        this.projectModel.components[projectComponent.name] = projectComponent;
	        return { component: projectComponent, footprintRef };
	    }
	    unionPartSignalsWhenModeAlreadyPopulated(projectComponent, component, partId) {
	        if (this.populatedComponentSignalDisplayModes.get(projectComponent)?.has(component.record.displayMode)) {
	            this.unionComponentSignals(projectComponent, this.getPartPins(component, partId), component.record.displayMode);
	        }
	    }
	    buildPartSymbol(component, sourcePartId, symbolName) {
	        const sourcePins = this.getPartPins(component, sourcePartId);
	        const symbolTransforms = this.createSymbolTransforms(component);
	        const pinTransforms = [
	            symbolTransforms[0],
	            {
	                pos: [0, 0],
	                angle: (component.record.orientation - 5) * -90,
	                mirrored: [false, false],
	            },
	            symbolTransforms[1],
	        ];
	        const sym = {
	            ref: symbolName,
	            name: symbolName,
	            drawings: [],
	            pins: [],
	            metadata: {
	                LibReference: component.record.libReference,
	            },
	        };
	        for (const pin of sourcePins) {
	            const convertedPin = AltiumShapeConverters_1.AltiumShapeConverters.convertPin(pin, component.record.orientation);
	            const pinDrawings = convertedPin.drawings;
	            const convertedPinGeometry = { ...convertedPin };
	            delete convertedPinGeometry.drawings;
	            const transformedPin = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(convertedPinGeometry, pinTransforms);
	            if (pinDrawings) {
	                transformedPin.drawings = pinDrawings.map((drawing, drawingIndex) => {
	                    const transformedDrawing = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(drawing.drawing, symbolTransforms);
	                    const isPinNameDrawing = pin.showPinName && drawingIndex === (pin.showDesignator ? 1 : 0);
	                    if (transformedDrawing.type === 'text' && component.record.isMirrored && !isPinNameDrawing) {
	                        transformedDrawing.justify = [-transformedDrawing.justify[0], transformedDrawing.justify[1]];
	                    }
	                    return {
	                        ...drawing,
	                        drawing: transformedDrawing,
	                    };
	                });
	            }
	            sym.pins.push(transformedPin);
	        }
	        return sym;
	    }
	    createSymbolTransforms(component) {
	        return [
	            {
	                pos: [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.y),
	                ],
	                angle: 0,
	                mirrored: [false, false],
	            },
	            { pos: [0, 0], angle: 0, mirrored: [false, component.record.isMirrored] },
	            { pos: [0, 0], angle: (component.record.orientation - 1) * -90, mirrored: [false, false] },
	        ];
	    }
	    getPartPins(component, partId) {
	        return component
	            .getPins()
	            .filter((pin) => (pin.ownerPartId === -1 || pin.ownerPartId === partId) &&
	            pin.ownerPartDisplayMode !== -1 &&
	            pin.ownerPartDisplayMode === component.record.displayMode);
	    }
	    materializePartSymbolDrawings(component, sourcePartId, symbol, recoverConversionFailures) {
	        const symbolTransforms = this.createSymbolTransforms(component);
	        symbol.drawings = component
	            .getGraphics()
	            .filter((graphic) => ((graphic.ownerPartId ?? 0) === sourcePartId || graphic.ownerPartId === -1) &&
	            (graphic.ownerPartDisplayMode ?? 0) === component.record.displayMode)
	            .flatMap((graphic) => {
	            let converted;
	            if (recoverConversionFailures) {
	                try {
	                    converted = this.convertGraphicRecord(graphic);
	                }
	                catch (error) {
	                    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
	                    this.schematicModel.warnings.push(`Failed to convert graphic record ${graphic.recordType} for component ${this.getComponentDesignator(component)} part ${sourcePartId}: ${errorMsg}`);
	                    return [];
	                }
	            }
	            else {
	                converted = this.convertGraphicRecord(graphic);
	            }
	            if (!converted) {
	                return [];
	            }
	            const layers = [(0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Symbol).id];
	            if (converted.type === 'rect' && converted.filled) {
	                return [
	                    {
	                        layers: [(0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Background).id],
	                        drawing: sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(converted, symbolTransforms),
	                    },
	                    {
	                        layers,
	                        drawing: sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing({ ...converted, strokeWidth: Math.max(converted.strokeWidth, 0.254), filled: false }, symbolTransforms),
	                    },
	                ];
	            }
	            return [
	                {
	                    layers,
	                    drawing: sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(converted.type === 'polygon' ? converted : { ...converted, filled: false }, symbolTransforms),
	                },
	            ];
	        });
	        return symbol;
	    }
	    reconcileSignalsForReplacedSymbol(projectComponent, discardedSymbol, replacementSymbol, component, partId) {
	        const stillRepresentedRefs = new Set(projectComponent.symbols
	            .filter((symbol) => symbol !== replacementSymbol)
	            .flatMap((symbol) => symbol.pins.map((pin) => pin.ref)));
	        const removedRefs = new Set(discardedSymbol.pins.map((pin) => pin.ref).filter((ref) => !stillRepresentedRefs.has(ref)));
	        for (const signal of projectComponent.signals) {
	            signal.pinRefs = signal.pinRefs.filter((ref) => ref === null || !removedRefs.has(ref));
	        }
	        projectComponent.signals = projectComponent.signals.filter((signal) => signal.pinRefs.length > 0 || signal.padRefs.length > 0);
	        this.unionComponentSignals(projectComponent, this.getPartPins(component, partId), component.record.displayMode);
	    }
	    symbolPinsMatch(left, right) {
	        if (left.pins.length !== right.pins.length) {
	            return false;
	        }
	        const leftPins = left.pins.slice().sort((a, b) => this.comparePinIdentityValues(a, b));
	        const rightPins = right.pins.slice().sort((a, b) => this.comparePinIdentityValues(a, b));
	        return leftPins.every((leftPin, index) => this.pinsMatch(leftPin, rightPins[index]));
	    }
	    pinsMatch(left, right) {
	        return (left.ref === right.ref &&
	            left.name === right.name &&
	            (left.hidden === true) === (right.hidden === true) &&
	            this.pinNumbersMatch(left.start, right.start) &&
	            this.pinNumbersMatch(left.end, right.end));
	    }
	    pinNumbersMatch(left, right) {
	        return left.length === right.length && left.every((value, index) => this.pinNumberMatches(value, right[index]));
	    }
	    pinNumberMatches(left, right) {
	        return Math.abs(left - right) <= 0.0001;
	    }
	    normalizePinCoordinate(value) {
	        const normalized = Math.abs(value) < 0.0001 ? 0 : Math.round(value / 0.0001) * 0.0001;
	        return Object.is(normalized, -0) ? 0 : normalized;
	    }
	    getAvailableVariantSymbolName(projectComponent, baseSymbolName, symbol) {
	        const pins = symbol.pins
	            .map((pin) => ({
	            ref: pin.ref,
	            name: pin.name,
	            hidden: pin.hidden === true,
	            start: [this.normalizePinCoordinate(pin.start[0]), this.normalizePinCoordinate(pin.start[1])],
	            end: [this.normalizePinCoordinate(pin.end[0]), this.normalizePinCoordinate(pin.end[1])],
	        }))
	            .sort((a, b) => this.comparePinIdentityValues(a, b));
	        const baseName = `${baseSymbolName}::Variant${sigma_engine_shared_1.Utils.fnv1aHash(JSON.stringify(pins))}`;
	        let ordinal = 1;
	        let variantName = baseName;
	        while (projectComponent.symbols.some((existingSymbol) => existingSymbol.ref === variantName)) {
	            ordinal += 1;
	            variantName = `${baseName}::${ordinal}`;
	        }
	        return variantName;
	    }
	    comparePinIdentityValues(left, right) {
	        for (const [leftValue, rightValue] of [
	            [left.ref, right.ref],
	            [left.name, right.name],
	            ...left.start.map((value, index) => [value, right.start[index]]),
	            ...left.end.map((value, index) => [value, right.end[index]]),
	            [left.hidden === true, right.hidden === true],
	        ]) {
	            if (leftValue < rightValue) {
	                return -1;
	            }
	            if (leftValue > rightValue) {
	                return 1;
	            }
	        }
	        return 0;
	    }
	    unionComponentSignals(projectComponent, allComponentPins, displayMode) {
	        const designatorsByName = new Map();
	        for (const pin of allComponentPins) {
	            const designators = designatorsByName.get(pin.name) ?? new Set();
	            designators.add(pin.designator);
	            designatorsByName.set(pin.name, designators);
	        }
	        const duplicateNames = new Set(Array.from(designatorsByName.entries())
	            .filter(([, designators]) => designators.size > 1)
	            .map(([name]) => name));
	        const signalsById = new Map(projectComponent.signals.map((signal) => [signal.id, signal]));
	        const signalsByPinRef = new Map(projectComponent.signals.flatMap((signal) => signal.pinRefs.map((pinRef) => [pinRef, signal])));
	        for (const pin of allComponentPins) {
	            if (pin.ownerPartDisplayMode === -1 || pin.ownerPartDisplayMode !== displayMode) {
	                continue;
	            }
	            if (signalsByPinRef.has(pin.designator)) {
	                continue;
	            }
	            const pinId = duplicateNames.has(pin.name) ? `${pin.designator}_${pin.name}` : pin.name;
	            let componentSignal = signalsById.get(pinId);
	            if (!componentSignal) {
	                componentSignal = {
	                    id: pinId,
	                    name: pin.name,
	                    pinRefs: [],
	                    padRefs: [],
	                };
	                signalsById.set(pinId, componentSignal);
	                projectComponent.signals.push(componentSignal);
	            }
	            if (!componentSignal.pinRefs.includes(pin.designator)) {
	                componentSignal.pinRefs.push(pin.designator);
	                signalsByPinRef.set(pin.designator, componentSignal);
	            }
	            switch (pin.electrical) {
	                case AltiumEnums_1.ElectricalType.Input:
	                    componentSignal.signalDirection = sigma_engine_shared_1.DesignModels.SignalDirection.Input;
	                    break;
	                case AltiumEnums_1.ElectricalType.Output:
	                    componentSignal.signalDirection = sigma_engine_shared_1.DesignModels.SignalDirection.Output;
	                    break;
	                case AltiumEnums_1.ElectricalType.IO:
	                    componentSignal.signalDirection = sigma_engine_shared_1.DesignModels.SignalDirection.BiDirectionalIO;
	                    break;
	                case AltiumEnums_1.ElectricalType.Power:
	                    componentSignal.attributes ??= {};
	                    componentSignal.attributes.power = true;
	                    break;
	                case AltiumEnums_1.ElectricalType.Passive:
	                case AltiumEnums_1.ElectricalType.OpenCollector:
	                case AltiumEnums_1.ElectricalType.OpenEmitter:
	                case AltiumEnums_1.ElectricalType.HiZ:
	                    break;
	            }
	        }
	    }
	    setComponentModelInformation(component, componentInfo) {
	        if (isJumperModelledComponentKind(component)) {
	            componentInfo.model = {
	                modelId: 'jumper.2h',
	            };
	        }
	        return componentInfo;
	    }
	    getComponentNameForAltiumComponent(component) {
	        const kindSuffix = isJumperModelledComponentKind(component) ? '::jumper' : '';
	        return `${this.getLibraryComponentIdentity(component)}${kindSuffix}`;
	    }
	    getLibraryComponentIdentity(component) {
	        if (component.record.designItemId) {
	            const symbolRevisionGuid = component.record.symbolRevisionGuid?.trim();
	            if (symbolRevisionGuid) {
	                return `${component.record.designItemId}::${symbolRevisionGuid}`;
	            }
	            if (component.record.partCount <= 2) {
	                return `${component.record.designItemId}::geometry-${this.getFallbackComponentGeometryHash(component)}`;
	            }
	            const multipartLibraryIdentity = JSON.stringify({
	                sourceLibraryName: component.record.sourceLibraryName,
	                libReference: component.record.libReference ?? 'Unknown',
	                partCount: component.record.partCount,
	            });
	            return `${component.record.designItemId}::multipart-${sigma_engine_shared_1.Utils.fnv1aHash(multipartLibraryIdentity)}`;
	        }
	        else {
	            return `${component.record.sourceLibraryName}::${component.record.libReference ?? 'Unknown'}::${component.record.uniqueId}`;
	        }
	    }
	    getFallbackComponentGeometryHash(component) {
	        const symbolTransforms = [
	            {
	                pos: [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.y),
	                ],
	                angle: 0,
	                mirrored: [false, false],
	            },
	            {
	                pos: [0, 0],
	                angle: 0,
	                mirrored: [false, component.record.isMirrored],
	            },
	            {
	                pos: [0, 0],
	                angle: (component.record.orientation - 1) * -90,
	                mirrored: [false, false],
	            },
	        ];
	        const pinTransforms = [
	            {
	                pos: [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(-component.record.location.y),
	                ],
	                angle: 0,
	                mirrored: [false, false],
	            },
	        ];
	        const mirrorTransform = {
	            pos: [0, 0],
	            angle: 0,
	            mirrored: [false, component.record.isMirrored],
	        };
	        const rotationTransform = {
	            pos: [0, 0],
	            angle: (component.record.orientation - 5) * -90,
	            mirrored: [false, false],
	        };
	        pinTransforms.push(mirrorTransform);
	        pinTransforms.push(rotationTransform);
	        const pins = component
	            .getPins()
	            .map((pin) => {
	            const convertedPin = AltiumShapeConverters_1.AltiumShapeConverters.convertPin(pin, component.record.orientation);
	            const convertedPinGeometry = { ...convertedPin };
	            delete convertedPinGeometry.drawings;
	            const transformedPin = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(convertedPinGeometry, pinTransforms);
	            return this.normalizeGeometrySignature({
	                ownerPartId: pin.ownerPartId,
	                ownerPartDisplayMode: pin.ownerPartDisplayMode,
	                ref: transformedPin.ref,
	                name: transformedPin.name,
	                number: transformedPin.number,
	                start: transformedPin.start,
	                end: transformedPin.end,
	                electrical: pin.electrical,
	                hidden: pin.hidden,
	                showPinName: pin.showPinName,
	                showDesignator: pin.showDesignator,
	            });
	        })
	            .map((pin) => JSON.stringify(pin))
	            .sort();
	        const graphics = component
	            .getGraphics()
	            .flatMap((graphic) => {
	            const converted = this.convertGraphicRecord(graphic);
	            if (!converted) {
	                return [];
	            }
	            const drawing = converted.type === 'polygon' ? converted : { ...converted, filled: false };
	            const transformedDrawing = sigma_engine_shared_1.Utils.Transform.applyTransformsToDrawing(drawing, symbolTransforms);
	            return [
	                {
	                    ownerPartId: graphic.ownerPartId,
	                    ownerPartDisplayMode: graphic.ownerPartDisplayMode,
	                    drawing: this.canonicalizeFingerprintDrawing(transformedDrawing),
	                },
	            ];
	        })
	            .map((graphic) => JSON.stringify(this.normalizeGeometrySignature(graphic)))
	            .sort();
	        const signature = JSON.stringify({
	            sourceLibraryName: component.record.sourceLibraryName,
	            libReference: component.record.libReference ?? 'Unknown',
	            partCount: component.record.partCount,
	            pins,
	            graphics,
	        });
	        return `${sigma_engine_shared_1.Utils.fnv1aHash(signature)}-${signature.length.toString(36)}`;
	    }
	    canonicalizeFingerprintDrawing(drawing) {
	        if (drawing.type !== 'rect') {
	            return drawing;
	        }
	        return {
	            ...drawing,
	            start: [Math.min(drawing.start[0], drawing.end[0]), Math.min(drawing.start[1], drawing.end[1])],
	            end: [Math.max(drawing.start[0], drawing.end[0]), Math.max(drawing.start[1], drawing.end[1])],
	        };
	    }
	    normalizeGeometrySignature(value) {
	        if (typeof value === 'number') {
	            return Number.isFinite(value) ? Number(value.toFixed(6)) : value;
	        }
	        if (Array.isArray(value)) {
	            return value.map((item) => this.normalizeGeometrySignature(item));
	        }
	        if (value && typeof value === 'object') {
	            return Object.fromEntries(Object.entries(value)
	                .sort(([a], [b]) => a.localeCompare(b))
	                .map(([key, entry]) => [key, this.normalizeGeometrySignature(entry)]));
	        }
	        return value ?? null;
	    }
	    getSymbolNameForAltiumComponent(component, partId = component.record.currentPartId) {
	        return `Part${String.fromCharCode('A'.charCodeAt(0) + partId - 1)}::Mode${component.record.displayMode ?? 0}::O${component.record.orientation ?? 0}::Mr${component.record.isMirrored ? 1 : 0}`;
	    }
	    isGroundName(name) {
	        const nameLower = name.trim().toLowerCase();
	        return (nameLower === 'gnd' ||
	            nameLower === 'ground' ||
	            nameLower.endsWith('gnd') ||
	            nameLower === 'vss' ||
	            nameLower === 'vssa' ||
	            nameLower === 'vssd');
	    }
	    extractPowerPorts(nameGenerator, schematicModel) {
	        const powerPorts = this.schematic.getPowerPorts();
	        if (powerPorts.length === 0) {
	            return;
	        }
	        for (const powerPort of powerPorts) {
	            const powerPortName = nameGenerator.generatePowerPortName(powerPort);
	            let componentRef = `POWERPORT_${powerPort.style}`;
	            if (powerPort.isCrossSheetConnector) {
	                componentRef += '_CROSSSHEET';
	            }
	            const symbolRef = `${powerPort.text}`;
	            let component = this.projectModel.components[componentRef];
	            if (!component) {
	                component = {
	                    name: componentRef,
	                    numPins: 1,
	                    numPads: 0,
	                    footprints: [],
	                    symbols: [],
	                    signals: [
	                        {
	                            id: '1',
	                            name: 'Port',
	                            pinRefs: ['1'],
	                            padRefs: [],
	                        },
	                    ],
	                    isVirtual: true,
	                    metadata: {
	                        'altium.powerPort.style': powerPort.style.toString(),
	                    },
	                };
	                this.projectModel.components[componentRef] = component;
	            }
	            if (!component.symbols.find((s) => s.ref === symbolRef)) {
	                try {
	                    const { graphics, labelYOffset } = AltiumShapeConverters_1.AltiumShapeConverters.convertPowerPort(powerPort);
	                    const symbol = {
	                        ref: symbolRef,
	                        name: symbolRef,
	                        drawings: graphics.map((drawing) => ({
	                            layers: [(0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.POWER_PORT).id],
	                            drawing,
	                        })),
	                        pins: [
	                            {
	                                type: 'pin',
	                                ref: '1',
	                                name: 'Port',
	                                number: '1',
	                                start: [0, 0],
	                                end: [0, 0],
	                                layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.PIN).id,
	                                isNetLabel: true,
	                            },
	                        ],
	                        metadata: {
	                            'altium.powerPort.text': powerPort.text,
	                            'altium.powerPort.style': powerPort.style.toString(),
	                            'altium.powerPort.labelY': labelYOffset,
	                        },
	                    };
	                    component.symbols.push(symbol);
	                }
	                catch (error) {
	                    this.processingResults.innerResults.push({
	                        type: 'altium-schematic-transformer',
	                        severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
	                        message: `Failed to create symbol for power port ${powerPort.text}: ${error instanceof Error ? error.message : String(error)}`,
	                        record: powerPort,
	                    });
	                    continue;
	                }
	            }
	            const symbol = component.symbols.find((s) => s.ref === symbolRef);
	            const labelYOffset = symbol?.metadata?.['altium.powerPort.labelY'];
	            if (!symbol || typeof labelYOffset !== 'number') {
	                throw new Error(`Symbol ${symbolRef} not found for component ${component.name}`);
	            }
	            let multiInstanceModule;
	            if (this.schematicModel.multiInstanceConfiguration) {
	                multiInstanceModule = {
	                    ref: powerPort.properties.uniqueId?.value ?? powerPort.index.toString(),
	                    name: powerPort.text,
	                    footprintRef: symbolRef,
	                    componentRef,
	                    center: [
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.x),
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.y),
	                    ],
	                    layer: 'F',
	                    transform: {
	                        pos: [0, 0],
	                        angle: powerPort.isCrossSheetConnector ? 0 : ((0, AltiumEnums_1.convertPowerPortOrientation)(powerPort.orientation) - 1) * -90,
	                        mirrored: [false, false],
	                    },
	                    pinNets: {},
	                    drawProperties: {
	                        value: {
	                            pos: [0, 0],
	                            layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.POWER_PORT).id,
	                            angle: ((0, AltiumEnums_1.convertPowerPortOrientation)(powerPort.orientation) - 1) * -90,
	                            justify: [0, 0],
	                            size: 1,
	                            thickness: 1,
	                            mirror: [false, false],
	                            hidden: !powerPort.showNetName,
	                            ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(powerPort.text),
	                        },
	                    },
	                    metadata: {
	                        'altium.powerPort.text': powerPort.text,
	                        'altium.powerPort.style': powerPort.style.toString(),
	                        'altium.powerPort.showNetName': powerPort.showNetName.toString(),
	                        'altium.index': powerPort.index.toString(),
	                    },
	                };
	                if (this.multiInstanceInstance) {
	                    this.combobulateModule(this.multiInstanceInstance, component, multiInstanceModule);
	                }
	                this.schematicModel.multiInstanceConfiguration.multiInstanceModules.push(multiInstanceModule);
	            }
	            for (const instance of this.instances) {
	                if (instance.isMultiChannelSchematicInstance) {
	                    continue;
	                }
	                const location = [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(powerPort.location.y),
	                ];
	                const valueProperty = {
	                    pos: sigma_engine_shared_1.Utils.Transform.translatePoint(location, sigma_engine_shared_1.Utils.Transform.rotatePoint([0, labelYOffset], (powerPort.orientation - 1) * -90)),
	                    layer: (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.POWER_PORT).id,
	                    angle: 0,
	                    justify: [0, 0],
	                    size: powerPort.isCrossSheetConnector ? 1.3 : 1.1,
	                    thickness: 1,
	                    mirror: [false, false],
	                    hidden: !powerPort.showNetName,
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(powerPort.text),
	                };
	                switch (powerPort.orientation) {
	                    case AltiumEnums_1.PortOrientation.RIGHTWARDS:
	                        if (powerPort.isCrossSheetConnector) {
	                            valueProperty.justify = [-1, 0];
	                        }
	                        else {
	                            valueProperty.justify = [-0.5, 0];
	                        }
	                        break;
	                    case AltiumEnums_1.PortOrientation.UPWARDS:
	                        valueProperty.justify = [0, -1];
	                        break;
	                    case AltiumEnums_1.PortOrientation.LEFTWARDS:
	                        if (powerPort.isCrossSheetConnector) {
	                            valueProperty.justify = [1, 0];
	                        }
	                        else {
	                            valueProperty.justify = [0.5, 0];
	                        }
	                        break;
	                    case AltiumEnums_1.PortOrientation.DOWNWARDS:
	                        valueProperty.justify = [0, 1];
	                        break;
	                }
	                const module = {
	                    ref: (0, PathUtils_1.joinPathUniqueIds)(instance.pathUniqueId, powerPort.properties.uniqueid?.value ?? powerPort.index.toString()),
	                    name: powerPortName,
	                    footprintRef: symbolRef,
	                    componentRef,
	                    center: location,
	                    layer: 'F',
	                    transform: {
	                        pos: [0, 0],
	                        angle: powerPort.isCrossSheetConnector ? 0 : ((0, AltiumEnums_1.convertPowerPortOrientation)(powerPort.orientation) - 1) * -90,
	                        mirrored: [false, false],
	                    },
	                    pinNets: {
	                        '1': powerPort.text,
	                    },
	                    drawProperties: {
	                        value: valueProperty,
	                    },
	                    metadata: {
	                        'altium.powerPort.text': powerPort.text,
	                        'altium.powerPort.style': powerPort.style.toString(),
	                        'altium.powerPort.showNetName': powerPort.showNetName.toString(),
	                        'altium.index': powerPort.index.toString(),
	                    },
	                    multiInstance: multiInstanceModule ?
	                        {
	                            instanceId: instance.pathUniqueId,
	                            displayModuleRef: multiInstanceModule.ref,
	                        }
	                        : undefined,
	                };
	                let modelId;
	                let voltageValue;
	                if (this.isGroundName(powerPort.text)) {
	                    modelId = 'ground.standard';
	                    voltageValue = undefined;
	                }
	                else {
	                    modelId = 'supply.standard';
	                    try {
	                        voltageValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(powerPort.text, sigma_engine_shared_1.CommonParameterUnits.voltage);
	                    }
	                    catch {
	                        voltageValue = undefined;
	                    }
	                }
	                let projectModule = this.projectModel.modules.find((m) => m.name === module.name);
	                if (!projectModule) {
	                    projectModule = {
	                        name: module.name,
	                        instances: [],
	                        componentRef: component.name,
	                        hideInBom: true,
	                        model: {
	                            modelId,
	                            inferenceReport: voltageValue !== undefined ?
	                                {
	                                    parameters: {
	                                        voltage: {
	                                            source: 'parser',
	                                            value: voltageValue,
	                                        },
	                                    },
	                                }
	                                : undefined,
	                        },
	                        value: powerPort.text,
	                        metadata: {
	                            'altium.powerPort': !powerPort.isCrossSheetConnector,
	                            'altium.crossSheetConnector': powerPort.isCrossSheetConnector,
	                        },
	                    };
	                    this.projectModel.modules.push(projectModule);
	                }
	                else if (voltageValue !== undefined &&
	                    (projectModule.model?.modelId !== modelId ||
	                        projectModule.model?.inferenceReport?.parameters?.voltage?.value !== voltageValue)) {
	                    throw new Error(`Conflicting model information for power port ${powerPort.text} in module ${module.name}`);
	                }
	                projectModule.instances.push({
	                    ref: module.ref,
	                    designId: this.schematicModel.designId,
	                });
	                schematicModel.modules.push(module);
	                const labelType = powerPort.isCrossSheetConnector ? 'off-sheet-connector' : 'power-port';
	                this.combobulateModule(instance, component, module, [
	                    {
	                        type: labelType,
	                        value: powerPort.text,
	                        pinRef: '1',
	                        metadata: {},
	                    },
	                ]);
	            }
	        }
	    }
	    extractAndCombobulateWiresAndJunctions(schematicModel) {
	        const wireLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.WIRE).id;
	        const junctionLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.JUNCTION).id;
	        schematicModel.drawings[wireLayerId] = schematicModel.drawings[wireLayerId] ?? [];
	        schematicModel.drawings[junctionLayerId] = schematicModel.drawings[junctionLayerId] ?? [];
	        const schematicWires = this.schematic.getWires().map((wire) => {
	            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertWire(wire);
	            schematicModel.drawings[wireLayerId].push(...converted);
	            return converted;
	        });
	        const junctionPositions = this.schematic.getJunctions().map((junction) => {
	            const junctionDrawing = AltiumShapeConverters_1.AltiumShapeConverters.convertJunction(junction);
	            schematicModel.drawings[junctionLayerId].push(junctionDrawing);
	            return junctionDrawing.position;
	        });
	        for (const instance of this.instances) {
	            const combobulator = this.getInstanceCombobulator(instance);
	            for (const wire of schematicWires) {
	                for (const track of wire) {
	                    const netRef = combobulator.addWire({ from: track.start, to: track.end });
	                    this.applyNetRefToGraphic(instance, track, netRef);
	                }
	            }
	            for (const position of junctionPositions) {
	                combobulator.addWire({ from: position, to: position });
	            }
	        }
	    }
	    extractBuses(schematicModel) {
	        const busLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.BUS).id;
	        schematicModel.drawings[busLayerId] = schematicModel.drawings[busLayerId] ?? [];
	        for (const bus of this.schematic.getBuses()) {
	            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertBus(bus);
	            schematicModel.drawings[busLayerId].push(...converted);
	        }
	        this.prepareRangedBusConductors();
	        const schematicBusEntries = this.schematic.getBusEntries().map((busEntry) => {
	            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertBusEntry(busEntry);
	            schematicModel.drawings[busLayerId].push(converted);
	            return converted;
	        });
	        for (const instance of this.instances) {
	            const combobulator = this.getInstanceCombobulator(instance);
	            for (const busEntry of schematicBusEntries) {
	                const netRef = combobulator.addWire({
	                    from: { position: busEntry.start, dimension: 'bus' },
	                    to: { position: busEntry.end, dimension: 'wire' },
	                });
	                this.applyNetRefToGraphic(instance, busEntry, netRef);
	            }
	        }
	    }
	    prepareRangedBusConductors() {
	        const hasRangedAnchor = this.schematic.records
	            .filter((record) => record.recordType === SchDocTypes_1.RecordType.NET_LABEL)
	            .some((record) => AltiumBusRange_1.AltiumBusRange.parse(record.text) !== undefined) ||
	            this.schematic.getPorts().some((record) => AltiumBusRange_1.AltiumBusRange.parse(record.name) !== undefined) ||
	            this.schematic
	                .getSheetSymbols()
	                .some((sheetSymbol) => sheetSymbol.getEntries().some((entry) => AltiumBusRange_1.AltiumBusRange.parse(entry.name) !== undefined)) ||
	            this.schematic
	                .getHarnessConnectors()
	                .some((connector) => connector.getEntries().some((entry) => AltiumBusRange_1.AltiumBusRange.parse(entry.name) !== undefined));
	        if (!hasRangedAnchor) {
	            return;
	        }
	        const eps = 0.02;
	        const segments = [];
	        for (const bus of this.schematic.getBuses()) {
	            const points = bus.points.map((p) => [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
	                CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
	            ]);
	            for (let i = 0; i < points.length - 1; i++) {
	                segments.push({ a: points[i], b: points[i + 1], island: segments.length });
	            }
	        }
	        const near = (p, q) => Math.abs(p[0] - q[0]) <= eps && Math.abs(p[1] - q[1]) <= eps;
	        const onSegment = (p, s) => {
	            const [ax, ay] = s.a;
	            const [bx, by] = s.b;
	            const length = Math.hypot(bx - ax, by - ay);
	            if (length === 0) {
	                return near(p, s.a);
	            }
	            const cross = (bx - ax) * (p[1] - ay) - (by - ay) * (p[0] - ax);
	            if (Math.abs(cross) / length > eps) {
	                return false;
	            }
	            const dot = (p[0] - ax) * (bx - ax) + (p[1] - ay) * (by - ay);
	            return dot >= -eps * length && dot <= length * length + eps * length;
	        };
	        const find = (i) => {
	            let root = i;
	            while (segments[root].island !== root) {
	                root = segments[root].island;
	            }
	            segments[i].island = root;
	            return root;
	        };
	        for (let i = 0; i < segments.length; i++) {
	            for (let j = i + 1; j < segments.length; j++) {
	                if (onSegment(segments[j].a, segments[i]) ||
	                    onSegment(segments[j].b, segments[i]) ||
	                    onSegment(segments[i].a, segments[j]) ||
	                    onSegment(segments[i].b, segments[j])) {
	                    segments[find(j)].island = find(i);
	                }
	            }
	        }
	        const anchors = [
	            ...this.schematic.records
	                .filter((record) => record.recordType === SchDocTypes_1.RecordType.NET_LABEL)
	                .flatMap((netLabel) => {
	                const range = AltiumBusRange_1.AltiumBusRange.parse(netLabel.text);
	                return range ?
	                    [
	                        {
	                            key: `net-label:${netLabel.index}`,
	                            position: [
	                                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
	                                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
	                            ],
	                            width: range.members.length,
	                        },
	                    ]
	                    : [];
	            }),
	            ...this.schematic.getPorts().flatMap((port) => {
	                const range = AltiumBusRange_1.AltiumBusRange.parse(port.name);
	                if (!range) {
	                    return [];
	                }
	                const portPosition = [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.y),
	                ];
	                return this.getPortConnectionPoints(port, portPosition).map((position) => ({
	                    key: `port:${port.index}`,
	                    position,
	                    width: range.members.length,
	                }));
	            }),
	            ...this.schematic.getSheetSymbols().flatMap((sheetSymbol) => sheetSymbol.getEntries().flatMap((entry) => {
	                const range = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
	                return range ?
	                    [
	                        {
	                            key: `sheet-entry:${entry.index}`,
	                            position: AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry),
	                            width: range.members.length,
	                        },
	                    ]
	                    : [];
	            })),
	            ...this.schematic.getHarnessConnectors().flatMap((connector) => connector.getEntries().flatMap((entry) => {
	                const range = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
	                return range ?
	                    [
	                        {
	                            key: AltiumSchematicTransformer$1.harnessEntryAnchorKey(entry),
	                            position: this.calculateHarnessEntryPosition(connector.record, entry),
	                            width: range.members.length,
	                        },
	                    ]
	                    : [];
	            })),
	        ];
	        const anchorsByIsland = new Map();
	        for (const anchor of anchors) {
	            const segmentIndex = segments.findIndex((segment) => onSegment(anchor.position, segment));
	            if (segmentIndex < 0) {
	                continue;
	            }
	            const island = find(segmentIndex);
	            const existing = anchorsByIsland.get(island) ?? [];
	            if (!existing.some((candidate) => candidate.key === anchor.key)) {
	                anchorsByIsland.set(island, [...existing, anchor]);
	            }
	        }
	        for (const [island, islandAnchors] of anchorsByIsland) {
	            if (islandAnchors.length < 2 || !islandAnchors.every((anchor) => anchor.width === islandAnchors[0].width)) {
	                continue;
	            }
	            for (let bitIndex = 0; bitIndex < islandAnchors[0].width; bitIndex++) {
	                const dimension = `altium-ranged-bus::${island}::${bitIndex}`;
	                for (const instance of this.instances) {
	                    const combobulator = this.getInstanceCombobulator(instance);
	                    for (let i = 0; i < segments.length; i++) {
	                        if (find(i) === island) {
	                            combobulator.addWire({
	                                from: { position: segments[i].a, dimension },
	                                to: { position: segments[i].b, dimension },
	                            });
	                        }
	                    }
	                }
	                for (const anchor of islandAnchors) {
	                    this.rangedBusDimensionsByAnchor.set(anchor.key, [
	                        ...(this.rangedBusDimensionsByAnchor.get(anchor.key) ?? []),
	                        dimension,
	                    ]);
	                }
	            }
	        }
	    }
	    markBusRangeTaps() {
	        const eps = 0.02;
	        const segments = [];
	        for (const bus of this.schematic.getBuses()) {
	            const points = bus.points.map((p) => [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
	                CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
	            ]);
	            for (let i = 0; i < points.length - 1; i++) {
	                segments.push({ a: points[i], b: points[i + 1], island: segments.length });
	            }
	        }
	        if (segments.length === 0) {
	            return;
	        }
	        const near = (p, q) => Math.abs(p[0] - q[0]) <= eps && Math.abs(p[1] - q[1]) <= eps;
	        const onSegment = (p, s) => {
	            const [ax, ay] = s.a;
	            const [bx, by] = s.b;
	            const cross = (bx - ax) * (p[1] - ay) - (by - ay) * (p[0] - ax);
	            const length = Math.hypot(bx - ax, by - ay);
	            if (length === 0) {
	                return near(p, s.a);
	            }
	            if (Math.abs(cross) / length > eps) {
	                return false;
	            }
	            const dot = (p[0] - ax) * (bx - ax) + (p[1] - ay) * (by - ay);
	            return dot >= -eps * length && dot <= length * length + eps * length;
	        };
	        const findIsland = (i) => {
	            let root = i;
	            while (segments[root].island !== root) {
	                root = segments[root].island;
	            }
	            segments[i].island = root;
	            return root;
	        };
	        for (let i = 0; i < segments.length; i++) {
	            for (let j = i + 1; j < segments.length; j++) {
	                if (onSegment(segments[j].a, segments[i]) ||
	                    onSegment(segments[j].b, segments[i]) ||
	                    onSegment(segments[i].a, segments[j]) ||
	                    onSegment(segments[i].b, segments[j])) {
	                    segments[findIsland(j)].island = findIsland(i);
	                }
	            }
	        }
	        const membersByIsland = new Map();
	        const netLabelRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.NET_LABEL);
	        for (const netLabel of netLabelRecords) {
	            const busRange = AltiumBusRange_1.AltiumBusRange.parse(netLabel.text);
	            if (!busRange) {
	                continue;
	            }
	            const anchorPos = [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
	                CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
	            ];
	            for (let i = 0; i < segments.length; i++) {
	                if (onSegment(anchorPos, segments[i])) {
	                    const island = findIsland(i);
	                    const members = membersByIsland.get(island) ?? [];
	                    members.push(...busRange.members);
	                    membersByIsland.set(island, members);
	                    break;
	                }
	            }
	        }
	        const addRangeAt = (position, members) => {
	            for (let i = 0; i < segments.length; i++) {
	                if (onSegment(position, segments[i])) {
	                    const island = findIsland(i);
	                    membersByIsland.set(island, [...(membersByIsland.get(island) ?? []), ...members]);
	                    break;
	                }
	            }
	        };
	        for (const port of this.schematic.getPorts()) {
	            const busRange = AltiumBusRange_1.AltiumBusRange.parse(port.name);
	            if (busRange) {
	                const portPosition = [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.y),
	                ];
	                for (const connectionPoint of this.getPortConnectionPoints(port, portPosition)) {
	                    addRangeAt(connectionPoint, busRange.members);
	                }
	            }
	        }
	        for (const sheetSymbol of this.schematic.getSheetSymbols()) {
	            for (const entry of sheetSymbol.getEntries()) {
	                const busRange = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
	                if (busRange) {
	                    addRangeAt(AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry), busRange.members);
	                }
	            }
	        }
	        if (membersByIsland.size === 0) {
	            return;
	        }
	        for (const [island, members] of membersByIsland) {
	            membersByIsland.set(island, Array.from(new Set(members)));
	        }
	        const markWireTap = (position, members) => {
	            for (const instance of this.instances) {
	                this.getInstanceCombobulator(instance).addWire({
	                    from: position,
	                    to: position,
	                    strategyMetadata: { busTapMembers: members },
	                });
	            }
	        };
	        for (const busEntry of this.schematic.getBusEntries()) {
	            const converted = AltiumShapeConverters_1.AltiumShapeConverters.convertBusEntry(busEntry);
	            for (let i = 0; i < segments.length; i++) {
	                const wireEnd = AltiumSchematicTransformer$1.getBusEntryWireEndpoint(converted, (position) => onSegment(position, segments[i]));
	                if (!wireEnd) {
	                    continue;
	                }
	                const members = membersByIsland.get(findIsland(i));
	                if (members) {
	                    markWireTap(wireEnd, members);
	                }
	                break;
	            }
	        }
	        for (const wire of this.schematic.getWires()) {
	            for (const track of AltiumShapeConverters_1.AltiumShapeConverters.convertWire(wire)) {
	                for (const endpoint of [track.start, track.end]) {
	                    for (let i = 0; i < segments.length; i++) {
	                        if (!onSegment(endpoint, segments[i])) {
	                            continue;
	                        }
	                        const members = membersByIsland.get(findIsland(i));
	                        if (!members) {
	                            break;
	                        }
	                        markWireTap(endpoint, members);
	                        break;
	                    }
	                }
	            }
	        }
	    }
	    static getBusEntryWireEndpoint(busEntry, isOnBus) {
	        if (isOnBus(busEntry.start)) {
	            return busEntry.end;
	        }
	        if (isOnBus(busEntry.end)) {
	            return busEntry.start;
	        }
	        return undefined;
	    }
	    extractSignalHarnesses(schematicModel) {
	        const signalHarnesses = this.schematic.getSignalHarnesses();
	        if (signalHarnesses.length === 0) {
	            return;
	        }
	        const signalHarnessLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.SIGNAL_HARNESS).id;
	        schematicModel.drawings[signalHarnessLayerId] = schematicModel.drawings[signalHarnessLayerId] ?? [];
	        for (const signalHarness of signalHarnesses) {
	            schematicModel.drawings[signalHarnessLayerId].push(...AltiumShapeConverters_1.AltiumShapeConverters.convertSignalHarness(signalHarness));
	        }
	        for (const instance of this.instances) {
	            const combobulator = this.getInstanceCombobulator(instance);
	            for (const signalHarness of signalHarnesses) {
	                for (const { from, to } of this.convertSignalHarnessToSegments(signalHarness)) {
	                    combobulator.addWire({
	                        from: { position: from, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
	                        to: { position: to, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
	                        strategyMetadata: {
	                            harness: {
	                                isCarrier: true,
	                                harnessSource: 'signal-harness',
	                            },
	                        },
	                    });
	                }
	            }
	        }
	    }
	    extractHarnessConnectors() {
	        for (const harnessConnector of this.schematic.getHarnessConnectors()) {
	            const harnessType = harnessConnector.getHarnessTypeText();
	            const entries = harnessConnector.getEntries();
	            if (entries.length === 0) {
	                continue;
	            }
	            for (const instance of this.instances) {
	                const entryPositions = entries.map((entry) => ({
	                    entry,
	                    position: this.calculateHarnessEntryPosition(harnessConnector.record, entry),
	                }));
	                for (const { entry, position } of entryPositions) {
	                    const bitDimensions = this.rangedBusDimensionsByAnchor.get(AltiumSchematicTransformer$1.harnessEntryAnchorKey(entry));
	                    const hasOrdinaryConnectivity = !!bitDimensions ||
	                        Array.from(this.getInstanceCombobulator(instance).defaultDimension.getObjectsConnectedToPosition(position)).some((object) => !object.label?.strategyMetadata?.harness?.isCarrier);
	                    if (this.isHarnessDefinitionEntry(entry) &&
	                        !!harnessType?.trim() &&
	                        harnessType?.trim() !== '*' &&
	                        !hasOrdinaryConnectivity) {
	                        this.processingResults.innerResults.push({
	                            type: 'info',
	                            message: `Harness definition entry ${entry.name} (${harnessType}) has no ordinary connectivity.`,
	                            record: entry,
	                        });
	                    }
	                    const bitMembers = bitDimensions ? AltiumBusRange_1.AltiumBusRange.parse(entry.name)?.members : undefined;
	                    const members = bitMembers && bitDimensions ?
	                        bitMembers.map((name, bitIndex) => ({ name, dimension: bitDimensions[bitIndex] }))
	                        : [{ name: entry.name, dimension: undefined }];
	                    for (const member of members) {
	                        this.setCarrierCombobulatorLabel(instance, position, member.name, 'net-label', {
	                            harness: {
	                                isCarrier: true,
	                                harnessType,
	                                harnessMemberName: member.name,
	                                harnessMemberType: this.getNestedHarnessEntryType(instance, entry, position),
	                                harnessMemberDimension: member.dimension,
	                                harnessSource: 'connector-entry',
	                                isHarnessDefinitionEntry: this.isHarnessDefinitionEntry(entry),
	                            },
	                        });
	                    }
	                }
	                const first = entryPositions[0]?.position;
	                if (!first) {
	                    continue;
	                }
	                const combobulator = this.getInstanceCombobulator(instance);
	                combobulator.addWire({
	                    from: {
	                        position: this.calculateHarnessConnectorPrimaryPosition(harnessConnector.record),
	                        dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness,
	                    },
	                    to: { position: first, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
	                    strategyMetadata: {
	                        harness: {
	                            isCarrier: true,
	                            harnessType,
	                            harnessSource: 'connector-internal',
	                        },
	                    },
	                });
	                for (const { position } of entryPositions.slice(1)) {
	                    combobulator.addWire({
	                        from: { position: first, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
	                        to: { position, dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness },
	                        strategyMetadata: {
	                            harness: {
	                                isCarrier: true,
	                                harnessType,
	                                harnessSource: 'connector-internal',
	                            },
	                        },
	                    });
	                }
	            }
	        }
	    }
	    extractHarnessConnectorDrawings(schematicModel) {
	        const harnessConnectors = this.schematic.getHarnessConnectors();
	        if (harnessConnectors.length === 0) {
	            return;
	        }
	        const symbolLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.HARNESS_CONNECTOR).id;
	        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.HARNESS_TYPE).id;
	        schematicModel.drawings[symbolLayerId] = schematicModel.drawings[symbolLayerId] ?? [];
	        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
	        const sheet = this.schematic.getSheet();
	        const textOptions = {
	            defaultProjectFontId: this.getProjectFontId(1),
	            reportedMissingFontIds: new Set(),
	        };
	        for (const harnessConnector of harnessConnectors) {
	            const connectorGroup = AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessConnector(harnessConnector.record);
	            if (!connectorGroup) {
	                continue;
	            }
	            for (const entry of harnessConnector.getEntries()) {
	                const entryPosition = this.calculateHarnessEntryPosition(harnessConnector.record, entry);
	                connectorGroup.drawings.push(AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessEntryMarker(entry, entryPosition));
	                const entryText = AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessEntryText(entry, entryPosition, sheet, this.getProjectFontId(entry.textFontId), textOptions);
	                if (entryText) {
	                    connectorGroup.drawings.push(entryText);
	                }
	            }
	            schematicModel.drawings[symbolLayerId].push(connectorGroup);
	            const harnessType = harnessConnector.getHarnessType();
	            if (!harnessType) {
	                continue;
	            }
	            const connectorPosition = [
	                CoordinateConverter_1.CoordinateConverter.milsToMm(harnessConnector.record.location.x),
	                CoordinateConverter_1.CoordinateConverter.milsToMm(harnessConnector.record.location.y),
	            ];
	            const typeLabel = AltiumShapeConverters_1.AltiumShapeConverters.convertHarnessType(harnessType, connectorPosition, sheet, this.getProjectFontId(harnessType.fontId), textOptions);
	            if (typeLabel) {
	                schematicModel.drawings[labelLayerId].push(typeLabel);
	            }
	        }
	    }
	    calculateHarnessConnectorPrimaryPosition(connector) {
	        const connectorX = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.x);
	        const connectorY = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.y);
	        const connectorWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.xSize);
	        const connectorHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.ySize);
	        const primaryConnectionPosition = CoordinateConverter_1.CoordinateConverter.milsToMm(CoordinateConverter_1.CoordinateConverter.parseFractionalCoordinate(connector.properties, 'primaryconnectionposition'));
	        const side = parseInt(connector.properties.harnessconnectorside?.value ?? '0', 10);
	        switch (side) {
	            case AltiumEnums_1.SheetSymbolSide.Left:
	                return [connectorX, connectorY + primaryConnectionPosition];
	            case AltiumEnums_1.SheetSymbolSide.Right:
	                return [connectorX + connectorWidth, connectorY + primaryConnectionPosition];
	            case AltiumEnums_1.SheetSymbolSide.Top:
	                return [connectorX + primaryConnectionPosition, connectorY];
	            case AltiumEnums_1.SheetSymbolSide.Bottom:
	                return [connectorX + primaryConnectionPosition, connectorY + connectorHeight];
	            default:
	                return [connectorX, connectorY + primaryConnectionPosition];
	        }
	    }
	    convertSignalHarnessToSegments(signalHarness) {
	        const points = signalHarness.points.map((point) => [
	            CoordinateConverter_1.CoordinateConverter.milsToMm(point.x),
	            CoordinateConverter_1.CoordinateConverter.milsToMm(point.y),
	        ]);
	        const segments = [];
	        for (let i = 0; i < points.length - 1; i++) {
	            segments.push({ from: points[i], to: points[i + 1] });
	        }
	        return segments;
	    }
	    calculateHarnessEntryPosition(connector, entry) {
	        const connectorX = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.x);
	        const connectorY = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.location.y);
	        const connectorWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.xSize);
	        const connectorHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(connector.ySize);
	        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.milsToMm(entry.distanceFromTop) * 10;
	        switch (entry.side) {
	            case AltiumEnums_1.SheetSymbolSide.Left:
	                return [connectorX, connectorY + distanceFromTop];
	            case AltiumEnums_1.SheetSymbolSide.Right:
	                return [connectorX + connectorWidth, connectorY + distanceFromTop];
	            case AltiumEnums_1.SheetSymbolSide.Top:
	                return [connectorX + distanceFromTop, connectorY];
	            case AltiumEnums_1.SheetSymbolSide.Bottom:
	                return [connectorX + distanceFromTop, connectorY + connectorHeight];
	            default:
	                return [connectorX, connectorY + distanceFromTop];
	        }
	    }
	    getNestedHarnessEntryType(instance, entry, position) {
	        if (!entry.harnessType) {
	            return undefined;
	        }
	        const combobulator = this.getInstanceCombobulator(instance);
	        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return undefined;
	        }
	        const touchesSignalHarness = combobulator
	            .getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)
	            .getObjectsAtPosition(position)
	            .some((object) => object.wire?.strategyMetadata?.harness?.harnessSource === 'signal-harness');
	        return touchesSignalHarness ? entry.harnessType : undefined;
	    }
	    static harnessEntryAnchorKey(entry) {
	        return `connector-entry:${entry.index}`;
	    }
	    isHarnessDefinitionEntry(entry) {
	        return entry.properties.ownerindexadditionallist?.value?.toUpperCase() === 'T';
	    }
	    applyNetRefToGraphic(instance, graphic, netRef) {
	        if (instance.isMultiChannelSchematicInstance) ;
	        else {
	            if (!Array.isArray(graphic.nets)) {
	                graphic.nets = [];
	            }
	            graphic.nets.push({
	                i: instance.pathUniqueId,
	                n: netRef.strCast(),
	            });
	        }
	    }
	    finalize(hierarchicalNetGroups) {
	        this.finalizeNets(hierarchicalNetGroups);
	        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(this.processingResults);
	        const moduleRefs = new Set(this.schematicModel.modules.map((m) => m.ref));
	        if (this.schematicModel.modules.length !== moduleRefs.size) {
	            const duplicateRefMap = this.schematicModel.modules.reduce((acc, m) => {
	                if (acc.has(m.ref)) {
	                    acc.get(m.ref).push(m);
	                }
	                else {
	                    acc.set(m.ref, [m]);
	                }
	                return acc;
	            }, new Map());
	            const duplicateRefEntries = Array.from(duplicateRefMap.entries()).filter(([, modules]) => modules.length > 1);
	            const duplicateRefList = duplicateRefEntries
	                .map(([ref, modules]) => `${ref} (${modules.length} times)`)
	                .join(', ');
	            throw new Error(`Duplicate module refs found in schematic ${this.schematicModel.designId}: ${duplicateRefList}`);
	        }
	    }
	    finalizeNets(hierarchicalNetGroups) {
	        if (this.instanceCombobulators.size !== this.instances.length) {
	            throw new Error('Not all instance nets have been generated');
	        }
	        for (const instance of this.instances) {
	            if (instance.isMultiChannelSchematicInstance) {
	                continue;
	            }
	            const combobulator = this.getInstanceCombobulator(instance);
	            const nets = combobulator.getFinalizedNets();
	            for (const net of nets.values()) {
	                const netGroup = hierarchicalNetGroups.find((group) => group.contains(net));
	                if (!netGroup) {
	                    throw new Error(`No net group for net ${net.name.valueOf()} in ${instance.pathUniqueId}`);
	                }
	                const netName = (netGroup.physicalName ?? net.name).valueOf();
	                const connections = netGroup.getConnections(instance);
	                for (const [i, nets] of netGroup.getNets()) {
	                    if (instance !== i) {
	                        continue;
	                    }
	                    for (const net of nets) {
	                        for (const netref of net.netRefs) {
	                            netref.setName(netName);
	                        }
	                    }
	                }
	                const onlyConnection = net.connections[0]?.connection;
	                const metadata = getNoConnectVirtualNetMetadata({
	                    connections: net.connections.length,
	                    wires: net.wires.length,
	                    labels: net.labels.length,
	                    connectionIsHiddenPin: onlyConnection !== undefined && this.isHiddenPinConnection(onlyConnection),
	                });
	                if (!this.schematicModel.nets[netName]) {
	                    this.schematicModel.nets[netName] = {
	                        name: netName,
	                        connections: [],
	                        localNames: [],
	                        ...(metadata && { metadata }),
	                    };
	                }
	                else if (metadata === undefined) {
	                    const aggregate = this.schematicModel.nets[netName];
	                    const aggregateMetadata = aggregate.metadata;
	                    if (aggregateMetadata?.[sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY] === sigma_engine_shared_1.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE) {
	                        delete aggregateMetadata[sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY];
	                        if (Object.keys(aggregateMetadata).length === 0) {
	                            delete aggregate.metadata;
	                        }
	                    }
	                }
	                const existingConnections = this.schematicModel.nets[netName].connections;
	                for (const connection of connections) {
	                    if (!existingConnections.some((c) => c.moduleRef === connection.moduleRef && c.pinRef === connection.pinRef)) {
	                        existingConnections.push(this.toPublicNetConnection(connection));
	                    }
	                }
	                for (const connection of connections) {
	                    const module = this.schematicModel.modules.find((m) => m.ref === connection.moduleRef);
	                    if (!module) {
	                        throw new Error(`Missing module ${connection.moduleRef} in design ${this.schematicModel.designId}`);
	                    }
	                    this.setModulePinNet(module, connection.pinRef, netName, this.isHiddenPinConnection(connection));
	                }
	            }
	        }
	    }
	    extractGraphics(schematicModel) {
	        const graphicRecords = this.schematic.getGraphics();
	        const templateIndices = new Set(this.schematic.getTemplates().map((t) => t.index));
	        const excludedComponentIndices = new Set(this.schematic
	            .getComponents()
	            .filter((component) => !this.isElectricallyEligible(component))
	            .map((component) => component.record.index));
	        const layerId = (0, layers_1.getAltiumSchLayerByType)(layers_1.AltiumSpecificSchLayerTypes.Drawings).id;
	        schematicModel.drawings[layerId] = schematicModel.drawings[layerId] ?? [];
	        for (const record of graphicRecords) {
	            const isOwned = record.ownerIndex !== -1;
	            const isTemplateOwned = templateIndices.has(record.ownerIndex);
	            if (isOwned && !isTemplateOwned && !excludedComponentIndices.has(record.ownerIndex)) {
	                continue;
	            }
	            try {
	                const drawing = this.convertGraphicRecord(record, false);
	                if (drawing) {
	                    schematicModel.drawings[isTemplateOwned ? (0, layers_1.getAltiumSchLayerByType)(layers_1.AltiumSpecificSchLayerTypes.TitleBlock).id : layerId].push(drawing);
	                }
	            }
	            catch (error) {
	                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
	                schematicModel.warnings.push(`Failed to convert graphic record ${record.recordType}: ${errorMsg}`);
	            }
	        }
	    }
	    convertGraphicRecord(record, invertY = false) {
	        switch (record.recordType) {
	            case SchDocTypes_1.RecordType.LINE:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertLine(record, invertY);
	            case SchDocTypes_1.RecordType.RECTANGLE:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertRectangle(record, invertY);
	            case SchDocTypes_1.RecordType.POLYLINE:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertPolyline(record, invertY);
	            case SchDocTypes_1.RecordType.POLYGON:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertPolygon(record, invertY);
	            case SchDocTypes_1.RecordType.ARC:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertArc(record, invertY);
	            case SchDocTypes_1.RecordType.BEZIER:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertBezier(record, invertY);
	            case SchDocTypes_1.RecordType.ELLIPSE:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertEllipse(record, invertY);
	            case SchDocTypes_1.RecordType.ELLIPTICAL_ARC:
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertEllipticalArc(record, invertY);
	            case SchDocTypes_1.RecordType.ROUND_RECTANGLE:
	                if (record.transparent) {
	                    return undefined;
	                }
	                return AltiumShapeConverters_1.AltiumShapeConverters.convertRoundRectangle(record, invertY);
	            default:
	                throw new Error(`Unsupported graphic record type: ${record.recordType}`);
	        }
	    }
	    extractImages(schematicModel) {
	        const imageRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.IMAGE);
	        if (imageRecords.length === 0) {
	            return;
	        }
	        const layerId = (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.DesignModels.SchLayerType.Image).id;
	        schematicModel.drawings[layerId] = schematicModel.drawings[layerId] ?? [];
	        const seenRefs = new Set(this.extractedImages.map((e) => e.ref));
	        for (const record of imageRecords) {
	            const storageName = record.filename;
	            if (!storageName) {
	                this.processingResults.innerResults.push({
	                    type: 'altium-schematic-transformer',
	                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
	                    message: 'IMAGE record has no filename',
	                });
	                continue;
	            }
	            let imageData = this.schematic.storage.get(storageName);
	            if (!imageData) {
	                this.processingResults.innerResults.push({
	                    type: 'altium-schematic-transformer',
	                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
	                    message: `IMAGE record references missing storage entry: ${storageName}`,
	                });
	                continue;
	            }
	            if (imageData[0] === 0x78 &&
	                (imageData[1] === 0x01 || imageData[1] === 0x5e || imageData[1] === 0x9c || imageData[1] === 0xda)) {
	                try {
	                    imageData = (0, fflate_1.unzlibSync)(imageData);
	                }
	                catch (error) {
	                    this.processingResults.innerResults.push({
	                        type: 'altium-schematic-transformer',
	                        severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
	                        message: `Failed to decompress zlib-compressed image data: ${error instanceof Error ? error.message : String(error)}`,
	                    });
	                    continue;
	                }
	            }
	            const isPng = imageData[0] === 0x89 && imageData[1] === 0x50 && imageData[2] === 0x4e && imageData[3] === 0x47;
	            const isJpeg = imageData[0] === 0xff && imageData[1] === 0xd8;
	            const isBmp = imageData[0] === 0x42 && imageData[1] === 0x4d;
	            const mimeType = isPng ? 'image/png'
	                : isJpeg ? 'image/jpeg'
	                    : isBmp ? 'image/bmp'
	                        : 'application/octet-stream';
	            const ext = isPng ? 'png'
	                : isJpeg ? 'jpg'
	                    : isBmp ? 'bmp'
	                        : 'bin';
	            const hash = sigma_engine_shared_1.Utils.fnv1aHash(storageName);
	            const ref = `images/${hash}.${ext}`;
	            const drawing = AltiumShapeConverters_1.AltiumShapeConverters.convertImage(record, ref);
	            schematicModel.drawings[layerId].push(drawing);
	            if (!seenRefs.has(ref)) {
	                seenRefs.add(ref);
	                this.extractedImages.push({
	                    ref,
	                    name: storageName,
	                    data: imageData,
	                    mimeType,
	                });
	            }
	        }
	    }
	    extractTextAndLabels(schematicModel) {
	        const sheet = this.schematic.getSheet();
	        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.LABEL).id;
	        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
	        const labelRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.LABEL);
	        const componentRecordIndexes = new Set(this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.COMPONENT).map((r) => r.index));
	        for (const label of labelRecords) {
	            if (componentRecordIndexes.has(label.ownerIndex)) {
	                const owningComponent = this.schematic.getComponentByIndex(label.ownerIndex);
	                if (!owningComponent) {
	                    continue;
	                }
	                if (label.ownerPartId !== undefined &&
	                    label.ownerPartId !== -1 &&
	                    label.ownerPartId !== owningComponent.record.currentPartId) {
	                    continue;
	                }
	            }
	            const projectFontId = this.getProjectFontId(label.fontId);
	            const resolvedLabel = this.resolveSpecialStringsInRecord(label, 'text');
	            const text = AltiumShapeConverters_1.AltiumShapeConverters.convertLabel(resolvedLabel, sheet, projectFontId);
	            schematicModel.drawings[labelLayerId].push(text);
	        }
	        const textFrameRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.TEXT_FRAME);
	        const textFrameLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.TEXT_FRAME).id;
	        for (const textFrame of textFrameRecords) {
	            const projectFontId = this.getProjectFontId(textFrame.fontId);
	            const resolvedTextFrame = this.resolveSpecialStringsInRecord(textFrame, 'text');
	            const text = AltiumShapeConverters_1.AltiumShapeConverters.convertTextFrame(resolvedTextFrame, sheet, projectFontId);
	            schematicModel.drawings[textFrameLayerId].push(text);
	        }
	        const netLabelRecords = this.schematic.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.NET_LABEL);
	        const netLabelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.NET_LABEL).id;
	        for (const netLabel of netLabelRecords) {
	            const projectFontId = this.getProjectFontId(netLabel.fontId);
	            const resolvedNetLabel = this.resolveSpecialStringsInRecord(netLabel, 'text');
	            const text = AltiumShapeConverters_1.AltiumShapeConverters.convertNetLabel(resolvedNetLabel, sheet, projectFontId);
	            schematicModel.drawings[netLabelLayerId].push(text);
	            if (netLabel.text.trim() === '') {
	                continue;
	            }
	            const busRange = AltiumBusRange_1.AltiumBusRange.parse(resolvedNetLabel.text);
	            if (busRange) {
	                for (const instance of this.instances) {
	                    const position = [
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
	                    ];
	                    busRange.members.forEach((member, memberIndex) => {
	                        const busDimension = this.rangedBusDimensionsByAnchor.get(`net-label:${netLabel.index}`)?.[memberIndex];
	                        this.getInstanceCombobulator(instance).setLabel([
	                            { position, dimension: `bus-range::${netLabel.index}::${memberIndex}` },
	                            ...(busDimension ? [{ position, dimension: busDimension }] : []),
	                        ], member, 'net-label', { busRangeMember: true, busRangeNetLabelMember: true });
	                    });
	                }
	                continue;
	            }
	            for (const instance of this.instances) {
	                const position = [
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.x),
	                    CoordinateConverter_1.CoordinateConverter.milsToMm(netLabel.location.y),
	                ];
	                if (this.isPositionOnHarnessCarrier(instance, position) &&
	                    !this.isPositionOnRegularWireOrBus(instance, position)) {
	                    this.setCarrierCombobulatorLabel(instance, position, resolvedNetLabel.text, 'net-label', {
	                        harness: {
	                            isCarrier: true,
	                            harnessSource: 'carrier-net-label',
	                        },
	                    });
	                }
	                else {
	                    const netRef = this.setCombobulatorLabel(instance, position, resolvedNetLabel.text, 'net-label', {});
	                    this.applyNetRefToGraphic(instance, text, netRef);
	                }
	            }
	        }
	    }
	    resolveSpecialStringsInRecord(record, textField, resolver) {
	        const effectiveResolver = resolver ?? this.specialStringResolver;
	        const resolvedText = effectiveResolver.resolve(record[textField]);
	        return { ...record, [textField]: resolvedText };
	    }
	    setCombobulatorLabel(instance, position, label, labelType, strategyMetadata) {
	        const combobulator = this.getInstanceCombobulator(instance);
	        if (this.enableDebugOutputs) {
	            this.schematicModel.drawings[(0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.SchLayerType.Other).id].push({
	                type: 'junction',
	                position,
	            });
	        }
	        return combobulator.setLabel(position, label, labelType, strategyMetadata);
	    }
	    setCombobulatorLabelInDimension(instance, position, dimension, label, labelType, strategyMetadata) {
	        const combobulator = this.getInstanceCombobulator(instance);
	        return combobulator.setLabel([{ position, dimension }], label, labelType, strategyMetadata);
	    }
	    setRangedHierarchyLabels(instance, positions, dimensionSeed, busRange, labelType, strategyMetadata) {
	        const combobulator = this.getInstanceCombobulator(instance);
	        let graphicNetRef;
	        for (const [index, member] of busRange.members.entries()) {
	            const dimension = `altium-range-member::${labelType}::${dimensionSeed}::${index}`;
	            const busDimension = this.rangedBusDimensionsByAnchor.get(`${labelType}:${dimensionSeed}`)?.[index];
	            const endpoints = [
	                ...positions.map((position) => ({ position, dimension })),
	                ...(busDimension ? positions.map((position) => ({ position, dimension: busDimension })) : []),
	            ];
	            const rangeMember = {
	                declaration: busRange.declaration,
	                index,
	                width: busRange.members.length,
	            };
	            const metadata = {
	                ...strategyMetadata,
	                [`${labelType}-metadata`]: {
	                    ...strategyMetadata[`${labelType}-metadata`],
	                    busRange: rangeMember,
	                },
	            };
	            const netRef = combobulator.setLabel(endpoints, member, labelType, metadata);
	            if (labelType === 'port' || !busDimension) {
	                combobulator.setLabel(endpoints, member, 'net-label', { busRangeMember: true });
	            }
	            graphicNetRef ??= netRef;
	        }
	        if (!graphicNetRef) {
	            throw new Error(`Cannot expand empty ${labelType} bus range`);
	        }
	        return graphicNetRef;
	    }
	    setCombobulatorLabelAtPositions(instance, positions, label, labelType, strategyMetadata) {
	        const endpoints = positions.map((position) => ({
	            position,
	            dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.Wire,
	        }));
	        const combobulator = this.getInstanceCombobulator(instance);
	        return combobulator.setLabel(endpoints, label, labelType, strategyMetadata);
	    }
	    setCarrierCombobulatorLabel(instance, position, label, labelType, strategyMetadata) {
	        return this.setCarrierCombobulatorLabelAtPositions(instance, [position], label, labelType, strategyMetadata);
	    }
	    setCarrierCombobulatorLabelAtPositions(instance, positions, label, labelType, strategyMetadata) {
	        const endpoints = positions.map((position) => ({
	            position,
	            dimension: AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness,
	        }));
	        const combobulator = this.getInstanceCombobulator(instance);
	        return combobulator.setLabel(endpoints, label, labelType, strategyMetadata);
	    }
	    isPositionOnHarnessCarrier(instance, position) {
	        const combobulator = this.getInstanceCombobulator(instance);
	        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return false;
	        }
	        const signalHarnessDimension = AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness;
	        const harnessDimension = combobulator.getDimension(signalHarnessDimension);
	        return Array.from(harnessDimension.getObjectsConnectedToPosition(position)).some((object) => object.wire?.strategyMetadata?.harness?.isCarrier ||
	            object.label?.strategyMetadata?.harness?.isCarrier ||
	            object.endpoints.some((endpoint) => endpoint.dimension === signalHarnessDimension));
	    }
	    isPositionOnRegularWireOrBus(instance, position) {
	        const combobulator = this.getInstanceCombobulator(instance);
	        const hasWireAtPosition = combobulator.defaultDimension
	            .getObjectsAtPosition(position)
	            .some((object) => !!object.wire);
	        const hasBusAtPosition = combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.Bus) &&
	            combobulator
	                .getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.Bus)
	                .getObjectsAtPosition(position)
	                .some((object) => !!object.wire);
	        return hasWireAtPosition || hasBusAtPosition;
	    }
	    getSheetSymbolTargetDesignId(sheetSymbol) {
	        for (const instance of this.instances) {
	            const child = instance.children.find((candidate) => candidate.sheetSymbol === sheetSymbol);
	            if (child) {
	                return child.hierarchyEntry.schematic.fileName;
	            }
	        }
	        return null;
	    }
	    extractSheetSymbols(schematicModel) {
	        const sheet = this.schematic.getSheet();
	        const sheetSymbols = this.schematic.getSheetSymbols();
	        const symbolLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.SHEET_SYMBOL).id;
	        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.LABEL).id;
	        const pinLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.SHEET_ENTRY).id;
	        schematicModel.drawings[symbolLayerId] = schematicModel.drawings[symbolLayerId] ?? [];
	        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
	        for (const sheetSymbol of sheetSymbols) {
	            try {
	                const fileName = sheetSymbol.getFileNameText();
	                const sheetName = sheetSymbol.getSheetNameText();
	                const sheetUniqueId = sheetSymbol.uniqueId;
	                const compName = `schref_${sheetUniqueId}`;
	                const sheetWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.xSize);
	                const sheetHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.ySize);
	                if (!this.projectModel.components[compName]) {
	                    const entries = sheetSymbol.getEntries();
	                    const pins = [];
	                    const sheetCenterX = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.x + sheetSymbol.record.xSize / 2);
	                    const sheetCenterY = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.y - sheetSymbol.record.ySize / 2);
	                    for (const entry of entries) {
	                        const pinName = entry.name;
	                        const entryPos = AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry);
	                        const relativePosX = entryPos[0] - sheetCenterX;
	                        const relativePosY = entryPos[1] - sheetCenterY - sheetHeight;
	                        const relativePos = [relativePosX, relativePosY];
	                        const pin = {
	                            type: 'pin',
	                            ref: pinName,
	                            name: pinName,
	                            number: pinName,
	                            start: relativePos,
	                            end: relativePos,
	                            layer: pinLayerId,
	                            drawings: [],
	                        };
	                        const iconDrawings = AltiumShapeConverters_1.AltiumShapeConverters.generateSheetEntryIconDrawings(entry, relativePosX, relativePosY);
	                        for (const drawing of iconDrawings.drawings) {
	                            pin.drawings?.push({
	                                layers: [symbolLayerId],
	                                drawing,
	                            });
	                        }
	                        const projectFontId = this.getProjectFontId(entry.textFontId);
	                        const entryLabel = AltiumShapeConverters_1.AltiumShapeConverters.convertSheetEntryLabel(entry, sheet, relativePos, [iconDrawings.width, iconDrawings.height], projectFontId);
	                        pin.drawings.push({
	                            layers: [labelLayerId],
	                            drawing: entryLabel,
	                        });
	                        pins.push(pin);
	                    }
	                    const symbol = {
	                        ref: compName,
	                        name: compName,
	                        pins: pins,
	                        drawings: [],
	                    };
	                    const centeredRect = {
	                        type: 'rect',
	                        start: [-sheetWidth / 2, -sheetHeight / 2],
	                        end: [sheetWidth / 2, sheetHeight / 2],
	                        strokeWidth: 0.127,
	                        filled: false,
	                    };
	                    symbol.drawings.push({
	                        layers: [symbolLayerId],
	                        drawing: centeredRect,
	                    });
	                    this.projectModel.components[compName] = {
	                        name: compName,
	                        footprints: [],
	                        numPads: 0,
	                        numPins: pins.length,
	                        symbols: [symbol],
	                        signals: pins.map((p) => ({ id: p.ref, name: p.name, pinRefs: [p.ref], padRefs: [] })),
	                        isVirtual: true,
	                    };
	                }
	                const sheetCenterX = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.x + sheetSymbol.record.xSize / 2);
	                const sheetCenterY = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.y + sheetSymbol.record.ySize / 2);
	                const drawModule = {
	                    ref: sheetUniqueId,
	                    name: sheetName,
	                    fullModuleName: sheetName,
	                    footprintRef: compName,
	                    componentRef: compName,
	                    center: [sheetCenterX, sheetCenterY],
	                    layer: 'F',
	                    transform: {
	                        pos: [0, 0],
	                        angle: 0,
	                        mirrored: [false, false],
	                    },
	                    pinNets: {},
	                    drawProperties: {},
	                    metadata: {
	                        'altium.uniqueId': sheetUniqueId,
	                        'altium.fileName': fileName,
	                        'altium.sheetName': sheetName,
	                    },
	                };
	                const sheetNameRecord = sheetSymbol.getSheetName();
	                const fileNameRecord = sheetSymbol.getFileName();
	                drawModule.drawProperties['sheetName'] = {
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(sheetNameRecord.text),
	                    pos: [
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(sheetNameRecord.location.x),
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(sheetNameRecord.location.y),
	                    ],
	                    layer: labelLayerId,
	                    angle: 0,
	                    justify: [-1, 1],
	                    thickness: 1,
	                    mirror: [false, false],
	                    hidden: sheetNameRecord.isHidden,
	                    ...this.schematic.getSheet().getFont(sheetNameRecord.fontId).textOpts,
	                };
	                drawModule.drawProperties['fileName'] = {
	                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(fileNameRecord.text),
	                    pos: [
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(fileNameRecord.location.x),
	                        CoordinateConverter_1.CoordinateConverter.milsToMm(fileNameRecord.location.y),
	                    ],
	                    layer: labelLayerId,
	                    angle: 0,
	                    justify: [-1, 1],
	                    thickness: 1,
	                    mirror: [false, false],
	                    hidden: fileNameRecord.isHidden,
	                    ...this.schematic.getSheet().getFont(fileNameRecord.fontId).textOpts,
	                };
	                if (this.enableDebugOutputs) {
	                    const xPos = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.record.location.x);
	                    drawModule.drawProperties['sheetUniqueId'] = {
	                        value: `Sheet: ${sheetUniqueId}`,
	                        pos: [xPos, sheetCenterY + sheetHeight / 2 + 1],
	                        layer: (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.DesignModels.SchLayerType.Other).id,
	                        angle: 0,
	                        justify: [-1, -1],
	                        size: 1.2,
	                        thickness: 1,
	                        mirror: [false, false],
	                    };
	                    const path = this.instances[0].pathUniqueId;
	                    drawModule.drawProperties['pathUniqueId'] = {
	                        value: `Path: ${path || '<root>'}`,
	                        pos: [xPos, sheetCenterY + sheetHeight / 2 + 3.2],
	                        layer: (0, layers_1.getAltiumSchLayerByType)(sigma_engine_shared_1.DesignModels.SchLayerType.Other).id,
	                        angle: 0,
	                        justify: [-1, -1],
	                        size: 1.2,
	                        thickness: 1,
	                        mirror: [false, false],
	                    };
	                }
	                schematicModel.modules.push(drawModule);
	                const schRef = {
	                    type: 'schref',
	                    file: fileName,
	                    name: sheetName,
	                    designId: this.getSheetSymbolTargetDesignId(sheetSymbol),
	                    drawModule: drawModule,
	                };
	                schematicModel.drawings[symbolLayerId].push(schRef);
	                const entries = sheetSymbol.getEntries();
	                for (const entry of entries) {
	                    for (const instance of this.instances) {
	                        const position = AltiumShapeConverters_1.AltiumShapeConverters.calculateSheetEntryPosition(sheetSymbol.record, entry);
	                        const repeatMatch = /^\s*REPEAT\((.*)\)\s*$/i.exec(entry.name)?.[1]?.trim();
	                        const childChannelIndexes = [];
	                        for (const childInstance of instance.children) {
	                            if (childInstance.sheetSymbol !== sheetSymbol || !childInstance.path.includes(instance)) {
	                                continue;
	                            }
	                            if (childChannelIndexes.indexOf(childInstance.channelIndex) !== -1) {
	                                throw new Error(`Channel index ${childInstance.channelIndex} is already in use for sheet entry ${entry.name}`);
	                            }
	                            childChannelIndexes.push(childInstance.channelIndex);
	                        }
	                        const sheetEntryMetadata = {
	                            sheetSymbolUniqueId: sheetUniqueId,
	                            parentPathUniqueId: instance.pathUniqueId,
	                            sheetSymbolName: sheetName,
	                            sheetSymbolFileName: fileName,
	                            sheetEntryName: repeatMatch ?? entry.name,
	                            sheetEntryText: entry.name,
	                            isRepeated: !!repeatMatch,
	                            channelIndexes: childChannelIndexes.sort(),
	                            ioType: entry.ioType,
	                        };
	                        const touchesSignalHarness = () => {
	                            const combobulator = this.getInstanceCombobulator(instance);
	                            if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	                                return false;
	                            }
	                            const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	                            return Array.from(harnessDimension.getObjectsAtPosition(position)).length > 0;
	                        };
	                        if (entry.harnessType || touchesSignalHarness()) {
	                            this.setCarrierCombobulatorLabel(instance, position, entry.name, 'sheet-entry', {
	                                'sheet-entry-metadata': sheetEntryMetadata,
	                                harness: {
	                                    isCarrier: true,
	                                    harnessType: entry.harnessType || undefined,
	                                    harnessSource: 'sheet-entry',
	                                },
	                            });
	                        }
	                        else {
	                            const busRange = AltiumBusRange_1.AltiumBusRange.parse(entry.name);
	                            if (busRange) {
	                                this.setRangedHierarchyLabels(instance, [position], String(entry.index), busRange, 'sheet-entry', {
	                                    'sheet-entry-metadata': sheetEntryMetadata,
	                                });
	                            }
	                            else {
	                                this.setCombobulatorLabel(instance, position, entry.name, 'sheet-entry', {
	                                    'sheet-entry-metadata': sheetEntryMetadata,
	                                });
	                            }
	                        }
	                    }
	                }
	            }
	            catch (error) {
	                this.processingResults.innerResults.push({
	                    type: 'altium-schematic-transformer',
	                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
	                    message: `Failed to process sheet symbol ${sheetSymbol.record.index}: ${error instanceof Error ? error.message : String(error)}`,
	                    record: sheetSymbol.record,
	                });
	            }
	        }
	    }
	    extractPorts(schematicModel) {
	        const ports = this.schematic.getPorts();
	        if (ports.length === 0) {
	            return;
	        }
	        const sheet = this.schematic.getSheet();
	        const labelLayerId = (0, layers_1.getAltiumSchLayerByRecordType)(SchDocTypes_1.RecordType.LABEL).id;
	        schematicModel.drawings[labelLayerId] = schematicModel.drawings[labelLayerId] ?? [];
	        for (const port of ports) {
	            try {
	                const projectFontId = this.getProjectFontId(port.fontId);
	                const portGroup = AltiumShapeConverters_1.AltiumShapeConverters.convertPort(port, sheet, projectFontId);
	                schematicModel.drawings[labelLayerId].push(portGroup);
	                const connectionPoints = this.getPortConnectionPoints(port, portGroup.pos);
	                for (const instance of this.instances) {
	                    const portMetadata = {
	                        portName: port.name,
	                        ioType: port.ioType,
	                        pathUniqueId: instance.pathUniqueId,
	                        channelIndex: instance.channelIndex,
	                    };
	                    const touchesSignalHarness = () => {
	                        const combobulator = this.getInstanceCombobulator(instance);
	                        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	                            return false;
	                        }
	                        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	                        return connectionPoints.some((point) => Array.from(harnessDimension.getObjectsAtPosition(point)).length > 0);
	                    };
	                    if (port.harnessType || touchesSignalHarness()) {
	                        this.setCarrierCombobulatorLabelAtPositions(instance, this.getHarnessPortCarrierPositions(portGroup.pos, connectionPoints), port.name, 'port', {
	                            'port-metadata': portMetadata,
	                            harness: {
	                                isCarrier: true,
	                                harnessType: port.harnessType || undefined,
	                                harnessSource: 'port',
	                            },
	                        });
	                    }
	                    else {
	                        const busRange = AltiumBusRange_1.AltiumBusRange.parse(port.name);
	                        const graphicNetRef = busRange ?
	                            this.setRangedHierarchyLabels(instance, connectionPoints, String(port.index), busRange, 'port', {
	                                'port-metadata': portMetadata,
	                            })
	                            : this.setCombobulatorLabelAtPositions(instance, connectionPoints, port.name, 'port', {
	                                'port-metadata': portMetadata,
	                            });
	                        this.applyNetRefToGraphic(instance, portGroup, graphicNetRef);
	                    }
	                }
	            }
	            catch (error) {
	                this.processingResults.innerResults.push({
	                    type: 'altium-schematic-transformer',
	                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
	                    message: `Failed to process port ${port.index}: ${error instanceof Error ? error.message : String(error)}`,
	                    record: port,
	                });
	            }
	        }
	    }
	    getPortConnectionPoints(port, portPosition) {
	        const portWidthMm = CoordinateConverter_1.CoordinateConverter.milsToMm(port.width);
	        const isVertical = port.style === AltiumEnums_1.PortStyle.NoneVertical ||
	            port.style === AltiumEnums_1.PortStyle.Top ||
	            port.style === AltiumEnums_1.PortStyle.Bottom ||
	            port.style === AltiumEnums_1.PortStyle.TopBottom;
	        const farEnd = isVertical ? [portPosition[0], portPosition[1] - portWidthMm] : [portPosition[0] + portWidthMm, portPosition[1]];
	        return this.dedupePositions([portPosition, farEnd]);
	    }
	    getHarnessPortCarrierPositions(portPosition, connectionPoints) {
	        return this.dedupePositions([portPosition, ...connectionPoints]);
	    }
	    dedupePositions(positions) {
	        const seen = new Set();
	        return positions.filter((position) => {
	            const key = `${position[0]}:${position[1]}`;
	            if (seen.has(key)) {
	                return false;
	            }
	            seen.add(key);
	            return true;
	        });
	    }
	    getInstanceCombobulator(instance) {
	        const combobulator = this.instanceCombobulators.get(instance);
	        if (!combobulator) {
	            throw new Error(`No combobulator found for instance ${instance?.pathUniqueId ?? 'null'}`);
	        }
	        return combobulator;
	    }
	    calculateBoundingBoxes(schematicModel) {
	        const layerSets = (0, layers_1.getAltiumDefaultLayerSets)();
	        const bbox = new Area_1.default();
	        bbox.extendFromSchModel(this.projectModel, schematicModel, layerSets[layers_1.ALTIUM_SCH_LAYERSET].layers, this.projectModel.warnings);
	        schematicModel.edges_bbox = {
	            minx: bbox.xMin,
	            miny: bbox.yMin,
	            maxx: bbox.xMax,
	            maxy: bbox.yMax,
	        };
	        const visibleBbox = new Area_1.default();
	        visibleBbox.extendFromSchModel(this.projectModel, schematicModel, layerSets[layers_1.ALTIUM_SCH_LAYERSET].layers, this.projectModel.warnings, false);
	        schematicModel.initial_zoombox = {
	            minx: visibleBbox.xMin,
	            miny: visibleBbox.yMin,
	            maxx: visibleBbox.xMax,
	            maxy: visibleBbox.yMax,
	        };
	    }
	}
	AltiumSchematicTransformer.AltiumSchematicTransformer = AltiumSchematicTransformer$1;
	
	return AltiumSchematicTransformer;
}

var SchematicInstanceTracker = {};

var hasRequiredSchematicInstanceTracker;

function requireSchematicInstanceTracker () {
	if (hasRequiredSchematicInstanceTracker) return SchematicInstanceTracker;
	hasRequiredSchematicInstanceTracker = 1;
	Object.defineProperty(SchematicInstanceTracker, "__esModule", { value: true });
	SchematicInstanceTracker.SchematicInstanceTracker = void 0;
	class SchematicInstanceTracker$1 {
	    constructor(sourceFile, projectConfigUtil) {
	        this.sourceFile = sourceFile;
	        this.projectConfigUtil = projectConfigUtil;
	        this.usedInstanceNumbers = new Map();
	        this.usedPathInstanceNumbers = new Map();
	    }
	    claimNextChannelIndex(channelPrefix, startingIndex) {
	        const usedInstanceNumbers = this.usedInstanceNumbers.get(channelPrefix) ?? new Set();
	        while (usedInstanceNumbers.has(startingIndex)) {
	            startingIndex++;
	        }
	        usedInstanceNumbers.add(startingIndex);
	        this.usedInstanceNumbers.set(channelPrefix, usedInstanceNumbers);
	        return startingIndex;
	    }
	    claimPreferredOrLowestAvailableChannelIndex(channelPrefix, preferredIndex) {
	        if (preferredIndex <= 0) {
	            throw new Error(`Preferred channel index must be positive, received ${preferredIndex}`);
	        }
	        const usedInstanceNumbers = this.usedInstanceNumbers.get(channelPrefix) ?? new Set();
	        let claimedIndex = preferredIndex;
	        if (usedInstanceNumbers.has(claimedIndex)) {
	            claimedIndex = 1;
	            while (usedInstanceNumbers.has(claimedIndex)) {
	                claimedIndex++;
	            }
	        }
	        usedInstanceNumbers.add(claimedIndex);
	        this.usedInstanceNumbers.set(channelPrefix, usedInstanceNumbers);
	        return claimedIndex;
	    }
	    setClaimedChannelIndex(channelPrefix, index) {
	        const usedInstanceNumbers = this.usedInstanceNumbers.get(channelPrefix) ?? new Set();
	        if (usedInstanceNumbers.has(index)) {
	            throw new Error(`Channel index ${index} is already in use for prefix ${channelPrefix}`);
	        }
	        usedInstanceNumbers.add(index);
	        this.usedInstanceNumbers.set(channelPrefix, usedInstanceNumbers);
	    }
	    claimNextSheetIndexInPath(parentInstance, thisHierarchy, startingIndex) {
	        const path = parentInstance.pathUniqueId;
	        if (parentInstance.path.length >= thisHierarchy.hierarchyIndex && !this.usedPathInstanceNumbers.has(path)) {
	            const pathParts = path.split('\\');
	            const parentPath = pathParts.slice(0, thisHierarchy.hierarchyIndex).join('\\');
	            const currentParent = this.usedPathInstanceNumbers.get(parentPath);
	            this.usedPathInstanceNumbers.set(path, new Set(currentParent));
	        }
	        const usedInstanceNumbers = this.usedPathInstanceNumbers.get(path) ?? new Set();
	        while (usedInstanceNumbers.has(startingIndex)) {
	            startingIndex++;
	        }
	        usedInstanceNumbers.add(startingIndex);
	        this.usedPathInstanceNumbers.set(path, usedInstanceNumbers);
	        return startingIndex;
	    }
	}
	SchematicInstanceTracker.SchematicInstanceTracker = SchematicInstanceTracker$1;
	
	return SchematicInstanceTracker;
}

var AltiumNetNamingStrategy = {};

var FallbackNetNamingRule = {};

var hasRequiredFallbackNetNamingRule;

function requireFallbackNetNamingRule () {
	if (hasRequiredFallbackNetNamingRule) return FallbackNetNamingRule;
	hasRequiredFallbackNetNamingRule = 1;
	Object.defineProperty(FallbackNetNamingRule, "__esModule", { value: true });
	FallbackNetNamingRule.FallbackNetNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	class FallbackNetNamingRule$1 {
	    constructor(defaultName) {
	        this.defaultName = defaultName;
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        const name = group.getLocalNames().sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
	        return name ?? this.defaultName;
	    }
	}
	FallbackNetNamingRule.FallbackNetNamingRule = FallbackNetNamingRule$1;
	
	return FallbackNetNamingRule;
}

var AltiumHierarchicalGroupingRule = {};

var hasRequiredAltiumHierarchicalGroupingRule;

function requireAltiumHierarchicalGroupingRule () {
	if (hasRequiredAltiumHierarchicalGroupingRule) return AltiumHierarchicalGroupingRule;
	hasRequiredAltiumHierarchicalGroupingRule = 1;
	Object.defineProperty(AltiumHierarchicalGroupingRule, "__esModule", { value: true });
	AltiumHierarchicalGroupingRule.AltiumHierarchicalGroupingRule = void 0;
	const project_1 = requireProject();
	const NetNamingRules_1 = requireNetNamingRules();
	class AltiumHierarchicalGroupingRule$1 {
	    constructor(config) {
	        this.config = config;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, hierarchy) {
	        if (this.config.mode === project_1.HierarchyMode.Hierarchical || this.config.mode === project_1.HierarchyMode.StrictHierarchical) {
	            this.mergeHierarchicalPortsRecursively(manager, hierarchy, undefined);
	        }
	        return manager;
	    }
	    mergeHierarchicalPortsRecursively(manager, childHierarchy, parentHierarchy) {
	        if (parentHierarchy) {
	            this.mergeHierarchicalPorts(manager, parentHierarchy, childHierarchy);
	        }
	        for (const child of childHierarchy.children) {
	            this.mergeHierarchicalPortsRecursively(manager, child, childHierarchy);
	        }
	    }
	    mergeHierarchicalPorts(manager, parentHierarchy, childHierarchy) {
	        const childSheetSymbol = childHierarchy.instance.sheetSymbol;
	        if (!childSheetSymbol) {
	            return;
	        }
	        const sheetSymbolFileName = childSheetSymbol.getFileNameText();
	        const sheetSymbolName = childSheetSymbol.getSheetNameText();
	        const childPortGroups = manager.findGroupsWithLabel('port', undefined, childHierarchy.instance);
	        const parentSheetEntryGroups = manager.findGroupsWithLabel('sheet-entry', undefined, parentHierarchy.instance);
	        for (const childGroup of childPortGroups) {
	            for (const [instance, nets] of childGroup.getNets()) {
	                if (instance.pathUniqueId !== childHierarchy.pathUniqueId) {
	                    continue;
	                }
	                const portLabels = nets.flatMap((net) => net.labels
	                    .filter((label) => label.labelType === 'port')
	                    .filter((l) => l.strategyMetadata?.['port-metadata']?.pathUniqueId === childHierarchy.pathUniqueId));
	                if (!portLabels.length) {
	                    continue;
	                }
	                for (const portLabel of portLabels) {
	                    const portMetadata = portLabel.strategyMetadata?.['port-metadata'];
	                    if (!portMetadata) {
	                        throw new Error(`Port label is missing metadata`);
	                    }
	                    const portName = portMetadata.portName;
	                    const portNameLower = portName.toLowerCase();
	                    for (const parentGroup of parentSheetEntryGroups) {
	                        for (const [parentInstance, nets] of parentGroup.getNets()) {
	                            if (parentInstance.pathUniqueId !== parentHierarchy.pathUniqueId) {
	                                continue;
	                            }
	                            const sheetEntryLabels = nets.flatMap((net) => net.labels
	                                .filter((label) => label.labelType === 'sheet-entry')
	                                .filter((l) => l.strategyMetadata?.['sheet-entry-metadata']?.parentPathUniqueId ===
	                                parentHierarchy.pathUniqueId &&
	                                l.strategyMetadata?.['sheet-entry-metadata']?.sheetSymbolUniqueId === childSheetSymbol.uniqueId));
	                            if (!sheetEntryLabels.length) {
	                                continue;
	                            }
	                            const hasMatchingSheetEntry = sheetEntryLabels.filter((label) => {
	                                if (!label.strategyMetadata?.['sheet-entry-metadata']) {
	                                    throw new Error(`Sheet entry label is missing metadata`);
	                                }
	                                const metadata = label.strategyMetadata['sheet-entry-metadata'];
	                                if (!this.harnessMetadataMatches(portLabel, label)) {
	                                    return false;
	                                }
	                                const portRange = portMetadata.busRange;
	                                const entryRange = metadata.busRange;
	                                if (portRange || entryRange) {
	                                    return (portRange !== undefined &&
	                                        entryRange !== undefined &&
	                                        portRange.declaration.toLowerCase() === entryRange.declaration.toLowerCase() &&
	                                        portRange.width === entryRange.width &&
	                                        portRange.index === entryRange.index &&
	                                        metadata.sheetSymbolFileName === sheetSymbolFileName &&
	                                        metadata.sheetSymbolName === sheetSymbolName);
	                                }
	                                if (metadata.isRepeated) {
	                                    return (metadata.sheetSymbolFileName === sheetSymbolFileName &&
	                                        metadata.sheetSymbolName === sheetSymbolName &&
	                                        metadata.sheetEntryName.toLowerCase() === portNameLower &&
	                                        metadata.channelIndex === portMetadata.channelIndex);
	                                }
	                                return (metadata.sheetSymbolFileName === sheetSymbolFileName &&
	                                    metadata.sheetSymbolName === sheetSymbolName &&
	                                    metadata.sheetEntryName.toLowerCase() === portNameLower);
	                            });
	                            if (hasMatchingSheetEntry.length) {
	                                manager.mergeGroups(parentGroup, childGroup);
	                                break;
	                            }
	                        }
	                    }
	                }
	            }
	        }
	    }
	    harnessMetadataMatches(portLabel, sheetEntryLabel) {
	        const portHarnessMember = portLabel.strategyMetadata?.harness?.harnessMemberName;
	        const sheetEntryHarnessMember = sheetEntryLabel.strategyMetadata?.harness?.harnessMemberName;
	        if (!portHarnessMember && !sheetEntryHarnessMember) {
	            return true;
	        }
	        if (!portHarnessMember || !sheetEntryHarnessMember) {
	            return false;
	        }
	        return portHarnessMember === sheetEntryHarnessMember || sheetEntryHarnessMember.endsWith(`.${portHarnessMember}`);
	    }
	}
	AltiumHierarchicalGroupingRule.AltiumHierarchicalGroupingRule = AltiumHierarchicalGroupingRule$1;
	
	return AltiumHierarchicalGroupingRule;
}

var AltiumPowerPortGroupingRule = {};

var AltiumLabelTypeNameUtil = {};

var hasRequiredAltiumLabelTypeNameUtil;

function requireAltiumLabelTypeNameUtil () {
	if (hasRequiredAltiumLabelTypeNameUtil) return AltiumLabelTypeNameUtil;
	hasRequiredAltiumLabelTypeNameUtil = 1;
	Object.defineProperty(AltiumLabelTypeNameUtil, "__esModule", { value: true });
	AltiumLabelTypeNameUtil.AltiumLabelTypeNameUtil = void 0;
	const AltiumLabelTypes_1 = requireAltiumLabelTypes();
	class AltiumLabelTypeNameUtil$1 {
	    static getLabelTypeName(labelType, name, argument) {
	        const args = [labelType, name];
	        if (argument !== undefined) {
	            args.push(argument);
	        }
	        return `LABEL_NAME(${args.map((a) => a.replace(/,/g, '\0$_comma_$\0')).join(',')})`;
	    }
	    static parseLabelTypeName(labelTypeName) {
	        if (!labelTypeName.startsWith('LABEL_NAME(') || !labelTypeName.endsWith(')')) {
	            throw new Error(`Not a label type name string`);
	        }
	        let match = labelTypeName.match(/^LABEL_NAME\((.+),(.+),(.+)\)$/);
	        if (!match) {
	            match = labelTypeName.match(/^LABEL_NAME\((.+),(.+)\)$/);
	            if (!match) {
	                throw new Error(`Invalid label type name: ${labelTypeName}`);
	            }
	        }
	        function uncomma(s) {
	            return s?.replace(/\0\$_comma_\$\0/g, ',');
	        }
	        return {
	            labelType: AltiumLabelTypes_1.AltiumLabelTypeNameGeneratorSchema.parse(uncomma(match[1])),
	            name: uncomma(match[2]),
	            argument: uncomma(match[3]),
	        };
	    }
	    static maybeDereferenceLabelName(name, ensureType) {
	        let derefed;
	        try {
	            derefed = AltiumLabelTypeNameUtil$1.parseLabelTypeName(name);
	        }
	        catch {
	            return name;
	        }
	        if (ensureType) {
	            if (derefed.labelType !== ensureType) {
	                throw new Error(`Expected label type ${ensureType}, got ${derefed.labelType}`);
	            }
	        }
	        return derefed.name;
	    }
	}
	AltiumLabelTypeNameUtil.AltiumLabelTypeNameUtil = AltiumLabelTypeNameUtil$1;
	
	return AltiumLabelTypeNameUtil;
}

var AltiumNetNameCaseSelection = {};

var hasRequiredAltiumNetNameCaseSelection;

function requireAltiumNetNameCaseSelection () {
	if (hasRequiredAltiumNetNameCaseSelection) return AltiumNetNameCaseSelection;
	hasRequiredAltiumNetNameCaseSelection = 1;
	Object.defineProperty(AltiumNetNameCaseSelection, "__esModule", { value: true });
	AltiumNetNameCaseSelection.compareAltiumNetNameCaseVariants = compareAltiumNetNameCaseVariants;
	AltiumNetNameCaseSelection.compareAltiumNetNameCandidates = compareAltiumNetNameCandidates;
	const sigma_engine_shared_1 = requireDist$1();
	function compareAltiumNetNameCaseVariants(a, b) {
	    if (a === b) {
	        return 0;
	    }
	    return a > b ? -1 : 1;
	}
	function compareAltiumNetNameCandidates(a, b) {
	    if (a.toLowerCase() === b.toLowerCase()) {
	        return compareAltiumNetNameCaseVariants(a, b);
	    }
	    return sigma_engine_shared_1.Utils.naturalStringSort(a, b);
	}
	
	return AltiumNetNameCaseSelection;
}

var hasRequiredAltiumPowerPortGroupingRule;

function requireAltiumPowerPortGroupingRule () {
	if (hasRequiredAltiumPowerPortGroupingRule) return AltiumPowerPortGroupingRule;
	hasRequiredAltiumPowerPortGroupingRule = 1;
	Object.defineProperty(AltiumPowerPortGroupingRule, "__esModule", { value: true });
	AltiumPowerPortGroupingRule.AltiumPowerPortGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const project_1 = requireProject();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	const AltiumNetNameCaseSelection_1 = requireAltiumNetNameCaseSelection();
	class AltiumPowerPortGroupingRule$1 {
	    constructor(config) {
	        this.config = config;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        const globalScope = Symbol('global-power-port-scope');
	        const powerGroupsByScopeAndName = new Map();
	        const powerNamesByScopeAndFormattedName = new Map();
	        const isStrictHierarchical = this.config.mode === project_1.HierarchyMode.StrictHierarchical;
	        for (const group of manager.findGroupsWithLabel('power-port')) {
	            for (const [instance, nets] of group.getNets()) {
	                for (const net of nets) {
	                    for (const label of net.labels) {
	                        if (label.labelType !== 'power-port') {
	                            continue;
	                        }
	                        const formattedLabel = label.formattedLabel;
	                        if (formattedLabel === undefined) {
	                            continue;
	                        }
	                        const scope = isStrictHierarchical ? instance : globalScope;
	                        const name = formattedLabel.toLowerCase();
	                        const authoredName = label.originalLabel.toLowerCase();
	                        const formattedName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(formattedLabel);
	                        const powerNames = powerNamesByScopeAndFormattedName.get(scope) ?? new Map();
	                        if (formattedName.toLowerCase() === authoredName) {
	                            powerNames.set(name, authoredName);
	                        }
	                        powerNamesByScopeAndFormattedName.set(scope, powerNames);
	                        const names = powerGroupsByScopeAndName.get(scope) ?? new Map();
	                        const groups = names.get(name) ?? new Set();
	                        groups.add(group);
	                        names.set(name, groups);
	                        powerGroupsByScopeAndName.set(scope, names);
	                    }
	                }
	            }
	        }
	        for (const [scope, groupsByName] of powerGroupsByScopeAndName) {
	            for (const [name, powerGroups] of groupsByName) {
	                const livePowerGroups = Array.from(new Set(Array.from(powerGroups).map((group) => manager.resolveGroup(group))));
	                const [powerGroup] = livePowerGroups;
	                if (powerGroup === undefined) {
	                    continue;
	                }
	                manager.mergeGroups(...livePowerGroups);
	                const powerName = powerNamesByScopeAndFormattedName.get(scope)?.get(name);
	                if (powerName === undefined) {
	                    continue;
	                }
	                if (typeof scope === 'symbol') {
	                    this.mergeMatchingNetLabels(manager, powerGroup, powerName);
	                }
	                else if (this.config.appendSheetNumberToLocalNets) {
	                    this.mergeMatchingNetLabels(manager, powerGroup, powerName, scope);
	                }
	            }
	        }
	        return manager;
	    }
	    mergeMatchingNetLabels(manager, powerGroup, caseFoldedPowerName, instance) {
	        const matchedLabelNames = [];
	        let absorbedNetLabelGroup = false;
	        for (const group of manager.findGroupsWithLabel('net-label')) {
	            const matchingNets = Array.from(group.getNets()).flatMap(([netInstance, nets]) => {
	                if (instance !== undefined && netInstance !== instance) {
	                    return [];
	                }
	                return nets.flatMap((net) => {
	                    const matchingLabels = net.labels.filter((label) => label.labelType === 'net-label' && label.originalLabel.toLowerCase() === caseFoldedPowerName);
	                    if (matchingLabels.length === 0) {
	                        return [];
	                    }
	                    matchedLabelNames.push(...matchingLabels.map((label) => label.originalLabel));
	                    return [[netInstance, net]];
	                });
	            });
	            if (matchingNets.length === 0) {
	                continue;
	            }
	            if (group !== powerGroup) {
	                manager.moveNetsToGroup(powerGroup, group, matchingNets);
	                absorbedNetLabelGroup = true;
	            }
	        }
	        if (absorbedNetLabelGroup && matchedLabelNames.length > 0) {
	            powerGroup.physicalName = matchedLabelNames.sort(AltiumNetNameCaseSelection_1.compareAltiumNetNameCaseVariants)[0];
	        }
	    }
	}
	AltiumPowerPortGroupingRule.AltiumPowerPortGroupingRule = AltiumPowerPortGroupingRule$1;
	
	return AltiumPowerPortGroupingRule;
}

var AltiumNetLabelGroupingRule = {};

var hasRequiredAltiumNetLabelGroupingRule;

function requireAltiumNetLabelGroupingRule () {
	if (hasRequiredAltiumNetLabelGroupingRule) return AltiumNetLabelGroupingRule;
	hasRequiredAltiumNetLabelGroupingRule = 1;
	Object.defineProperty(AltiumNetLabelGroupingRule, "__esModule", { value: true });
	AltiumNetLabelGroupingRule.AltiumNetLabelGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const project_1 = requireProject();
	class AltiumNetLabelGroupingRule$1 {
	    constructor(config) {
	        this.config = config;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	        if (this.config.mode !== project_1.HierarchyMode.Global) {
	            throw new Error(`AltiumNetLabelGroupingRule should only be used in Global mode. ` +
	                `In other modes, net labels are local to their sheet.`);
	        }
	    }
	    apply(manager, hierarchy) {
	        this.mergeNetLabelsGlobally(manager, hierarchy);
	        return manager;
	    }
	    mergeNetLabelsGlobally(manager, hierarchy) {
	        const netLabelGroups = new Map();
	        for (const group of manager.findGroupsWithLabel('net-label')) {
	            const netLabels = group.getNetLabels().filter((label) => label.labelType === 'net-label');
	            for (const label of netLabels) {
	                const netLabelName = label.formattedLabel;
	                if (!netLabelName) {
	                    continue;
	                }
	                if (!netLabelGroups.has(netLabelName)) {
	                    netLabelGroups.set(netLabelName, new Set());
	                }
	                netLabelGroups.get(netLabelName).add(group);
	            }
	        }
	        for (const groups of netLabelGroups.values()) {
	            if (groups.size <= 1) {
	                continue;
	            }
	            const groupsArray = Array.from(groups);
	            manager.mergeGroups(...groupsArray);
	        }
	        for (const child of hierarchy.children) {
	            this.mergeNetLabelsGlobally(manager, child);
	        }
	    }
	}
	AltiumNetLabelGroupingRule.AltiumNetLabelGroupingRule = AltiumNetLabelGroupingRule$1;
	
	return AltiumNetLabelGroupingRule;
}

var AltiumPortGroupingRule = {};

var hasRequiredAltiumPortGroupingRule;

function requireAltiumPortGroupingRule () {
	if (hasRequiredAltiumPortGroupingRule) return AltiumPortGroupingRule;
	hasRequiredAltiumPortGroupingRule = 1;
	Object.defineProperty(AltiumPortGroupingRule, "__esModule", { value: true });
	AltiumPortGroupingRule.AltiumPortGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const project_1 = requireProject();
	class AltiumPortGroupingRule$1 {
	    constructor(config) {
	        this.config = config;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	        if (this.config.mode !== project_1.HierarchyMode.Flat && this.config.mode !== project_1.HierarchyMode.Global) {
	            throw new Error(`AltiumPortGroupingRule should only be used in Flat or Global mode. ` +
	                `In hierarchical modes, ports bind through matching sheet entries.`);
	        }
	    }
	    apply(manager, _hierarchy) {
	        const groupsByPortKey = new Map();
	        for (const group of manager.findGroupsWithLabel('port')) {
	            for (const label of group.getNetLabels()) {
	                const portKey = this.getProjectWidePortKey(label);
	                if (!portKey) {
	                    continue;
	                }
	                let groups = groupsByPortKey.get(portKey);
	                if (!groups) {
	                    groups = new Set();
	                    groupsByPortKey.set(portKey, groups);
	                }
	                groups.add(group);
	            }
	        }
	        for (const groups of groupsByPortKey.values()) {
	            if (groups.size > 1) {
	                const [target, ...toMerge] = groups;
	                manager.mergeGroups(target, ...toMerge);
	                for (const group of toMerge) {
	                    for (const label of group.getNetLabels()) {
	                        const portKey = this.getProjectWidePortKey(label);
	                        if (portKey) {
	                            groupsByPortKey.get(portKey)?.delete(group);
	                        }
	                    }
	                }
	                for (const label of target.getNetLabels()) {
	                    const portKey = this.getProjectWidePortKey(label);
	                    if (portKey) {
	                        groupsByPortKey.get(portKey)?.add(target);
	                    }
	                }
	            }
	        }
	        return manager;
	    }
	    getProjectWidePortKey(label) {
	        if (label.labelType !== 'port' || label.strategyMetadata?.harness?.isCarrier) {
	            return undefined;
	        }
	        const portName = label.strategyMetadata?.['port-metadata']?.portName?.toLowerCase();
	        if (!portName) {
	            return undefined;
	        }
	        const harness = label.strategyMetadata?.harness;
	        if (harness?.harnessSource !== 'synthetic-port') {
	            return `plain:${portName}`;
	        }
	        const memberName = harness.harnessMemberName?.toLowerCase();
	        return memberName ? `synthetic:${portName}\u0000${memberName}` : undefined;
	    }
	}
	AltiumPortGroupingRule.AltiumPortGroupingRule = AltiumPortGroupingRule$1;
	
	return AltiumPortGroupingRule;
}

var AltiumOffSheetConnectorGroupingRule = {};

var hasRequiredAltiumOffSheetConnectorGroupingRule;

function requireAltiumOffSheetConnectorGroupingRule () {
	if (hasRequiredAltiumOffSheetConnectorGroupingRule) return AltiumOffSheetConnectorGroupingRule;
	hasRequiredAltiumOffSheetConnectorGroupingRule = 1;
	Object.defineProperty(AltiumOffSheetConnectorGroupingRule, "__esModule", { value: true });
	AltiumOffSheetConnectorGroupingRule.AltiumOffSheetConnectorGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const project_1 = requireProject();
	class AltiumOffSheetConnectorGroupingRule$1 {
	    constructor(config) {
	        this.config = config;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, hierarchy) {
	        if (this.config.mode === project_1.HierarchyMode.Flat || this.config.mode === project_1.HierarchyMode.Global) {
	            this.mergeOffSheetConnectorsGlobally(manager, hierarchy);
	        }
	        else {
	            this.mergeOffSheetConnectorsHierarchically(manager, hierarchy);
	        }
	        return manager;
	    }
	    mergeOffSheetConnectorsGlobally(manager, hierarchy) {
	        const offSheetGroups = new Map();
	        for (const group of manager.findGroupsWithLabel('off-sheet-connector')) {
	            const offSheetLabels = group.getNetLabels().filter((label) => label.labelType === 'off-sheet-connector');
	            for (const label of offSheetLabels) {
	                if (!label.formattedLabel) {
	                    continue;
	                }
	                const connectorName = label.formattedLabel;
	                if (!offSheetGroups.has(connectorName)) {
	                    offSheetGroups.set(connectorName, new Set());
	                }
	                offSheetGroups.get(connectorName).add(group);
	            }
	        }
	        for (const groups of offSheetGroups.values()) {
	            if (groups.size <= 1) {
	                continue;
	            }
	            const groupsArray = Array.from(groups);
	            manager.mergeGroups(...groupsArray);
	        }
	        for (const child of hierarchy.children) {
	            this.mergeOffSheetConnectorsGlobally(manager, child);
	        }
	    }
	    mergeOffSheetConnectorsHierarchically(manager, hierarchy) {
	        const offsheetConnectors = new Map();
	        for (const group of manager.findGroupsWithLabel('off-sheet-connector')) {
	            let isInThisHierarchy = false;
	            for (const [instance] of group.getNets()) {
	                if (instance.pathUniqueId === hierarchy.pathUniqueId ||
	                    instance.pathUniqueId.startsWith(hierarchy.pathUniqueId + '/')) {
	                    isInThisHierarchy = true;
	                    break;
	                }
	            }
	            if (!isInThisHierarchy) {
	                continue;
	            }
	            for (const label of group.getNetLabels()) {
	                if (label.labelType === 'off-sheet-connector') {
	                    if (!label.formattedLabel) {
	                        continue;
	                    }
	                    if (!offsheetConnectors.has(label.formattedLabel)) {
	                        offsheetConnectors.set(label.formattedLabel, []);
	                    }
	                    offsheetConnectors.get(label.formattedLabel).push(group);
	                }
	            }
	        }
	        for (const groups of offsheetConnectors.values()) {
	            if (groups.length <= 1) {
	                continue;
	            }
	            const uniqueGroups = Array.from(new Set(groups));
	            if (uniqueGroups.length <= 1) {
	                continue;
	            }
	            manager.mergeGroups(...uniqueGroups);
	        }
	        for (const child of hierarchy.children) {
	            this.mergeOffSheetConnectorsHierarchically(manager, child);
	        }
	    }
	}
	AltiumOffSheetConnectorGroupingRule.AltiumOffSheetConnectorGroupingRule = AltiumOffSheetConnectorGroupingRule$1;
	
	return AltiumOffSheetConnectorGroupingRule;
}

var AltiumHierarchyPriorityNamingRule = {};

var AltiumNetNamingRule = {};

var hasRequiredAltiumNetNamingRule;

function requireAltiumNetNamingRule () {
	if (hasRequiredAltiumNetNamingRule) return AltiumNetNamingRule;
	hasRequiredAltiumNetNamingRule = 1;
	Object.defineProperty(AltiumNetNamingRule, "__esModule", { value: true });
	AltiumNetNamingRule.AltiumNetNamingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	class AltiumNetNamingRule$1 {
	    constructor(config, namingStrategy) {
	        this.config = config;
	        this.namingStrategy = namingStrategy;
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    dereferenceLabelName(name) {
	        try {
	            const result = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.parseLabelTypeName(name);
	            return result.name;
	        }
	        catch {
	            return name;
	        }
	    }
	}
	AltiumNetNamingRule.AltiumNetNamingRule = AltiumNetNamingRule$1;
	
	return AltiumNetNamingRule;
}

var AltiumSheetEntryNamingEligibility = {};

var hasRequiredAltiumSheetEntryNamingEligibility;

function requireAltiumSheetEntryNamingEligibility () {
	if (hasRequiredAltiumSheetEntryNamingEligibility) return AltiumSheetEntryNamingEligibility;
	hasRequiredAltiumSheetEntryNamingEligibility = 1;
	Object.defineProperty(AltiumSheetEntryNamingEligibility, "__esModule", { value: true });
	AltiumSheetEntryNamingEligibility.isAltiumSheetEntryNamingEligible = isAltiumSheetEntryNamingEligible;
	AltiumSheetEntryNamingEligibility.isAltiumPairedChildPortNamingEligible = isAltiumPairedChildPortNamingEligible;
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const eligibilityIndexes = new WeakMap();
	function isAltiumSheetEntryNamingEligible(group, sheetEntryLabel, hierarchyMode, includeMatchingPeer = true) {
	    const index = getEligibilityIndex(group, hierarchyMode);
	    const regime = includeMatchingPeer ? index.withPeer : index.labelOnly;
	    return regime.sheetEntryEligibility.get(sheetEntryLabel) ?? false;
	}
	function isAltiumPairedChildPortNamingEligible(group, portLabel, hierarchyMode, includeMatchingPeer = true, allowRelayBridgeRescue = false) {
	    const portMetadata = portLabel.strategyMetadata?.['port-metadata'];
	    if (!portMetadata) {
	        return true;
	    }
	    const index = getEligibilityIndex(group, hierarchyMode);
	    const instance = index.labelInstances.get(portLabel);
	    if (instance?.pathUniqueId !== portMetadata.pathUniqueId || !instance.sheetSymbol) {
	        return true;
	    }
	    const regime = includeMatchingPeer ? index.withPeer : index.labelOnly;
	    const pair = getPairKey(instance.sheetSymbol.uniqueId, portMetadata.portName);
	    const repeatedPair = getRepeatedPairKey(pair, portMetadata.channelIndex);
	    const isLabelOnlyIneligible = index.labelOnly.ineligibleUnscopedPairs.has(pair);
	    const isPeerEligible = !index.withPeer.ineligibleUnscopedPairs.has(pair);
	    const localNet = index.labelNets.get(portLabel);
	    if (isLabelOnlyIneligible &&
	        isPeerEligible &&
	        localNet?.netObjects.some((object) => object.connection) &&
	        hasDifferentHierarchyIdentity(group, portMetadata.portName)) {
	        return true;
	    }
	    if (allowRelayBridgeRescue && isAltiumStrictRelayBridgeChildPort(portLabel, hierarchyMode, index)) {
	        return true;
	    }
	    return !regime.ineligibleUnscopedPairs.has(pair) && !regime.ineligibleRepeatedPairs.has(repeatedPair);
	}
	function isAltiumStrictRelayBridgeChildPort(portLabel, hierarchyMode, index) {
	    if (hierarchyMode !== altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
	        return false;
	    }
	    const portMetadata = portLabel.strategyMetadata?.['port-metadata'];
	    const instance = index.labelInstances.get(portLabel);
	    const localNet = index.labelNets.get(portLabel);
	    if (!portMetadata || !instance?.sheetSymbol || !localNet) {
	        return false;
	    }
	    const parentEntries = Array.from(index.labelNets.keys()).filter((label) => isPairedPlainScalarEntry(label, instance.sheetSymbol.uniqueId, portMetadata.portName, instance.pathUniqueId));
	    if (parentEntries.length !== 1) {
	        return false;
	    }
	    const parentEntry = parentEntries[0];
	    const parentNet = index.labelNets.get(parentEntry);
	    const bridgeEntryLabels = parentNet?.netObjects.map((object) => object.label).filter(isSheetEntry) ?? [];
	    const bridgeEntries = bridgeEntryLabels.filter(isPlainScalarSheetEntry);
	    if (!parentNet ||
	        !parentNet.netObjects.some((object) => object.wire) ||
	        parentNet.netObjects.some((object) => object.connection) ||
	        bridgeEntries.length !== bridgeEntryLabels.length ||
	        bridgeEntries.length < 2) {
	        return false;
	    }
	    const childPorts = bridgeEntries
	        .map((entry) => findPairedChildPort(entry, index))
	        .filter((candidate) => candidate !== undefined);
	    if (childPorts.length !== bridgeEntries.length) {
	        return false;
	    }
	    const childSides = childPorts.map((childPort) => index.labelNets.get(childPort));
	    if (childSides.some((net) => !net || !(net.netObjects.some((object) => object.connection) || hasGenuineRelayContinuation(net, index)))) {
	        return false;
	    }
	    return childPorts.some((childPort) => hasGenuineRelayContinuation(index.labelNets.get(childPort), index));
	}
	function isPlainScalarSheetEntry(label) {
	    const metadata = label?.strategyMetadata?.['sheet-entry-metadata'];
	    return (label?.labelType === 'sheet-entry' &&
	        metadata?.isRepeated === false &&
	        metadata.busRange === undefined &&
	        label.strategyMetadata?.busRangeMember !== true &&
	        label.strategyMetadata?.harness === undefined);
	}
	function isPairedPlainScalarEntry(label, sheetSymbolUniqueId, portName, childPathUniqueId) {
	    const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
	    return (isPlainScalarSheetEntry(label) &&
	        metadata?.sheetSymbolUniqueId === sheetSymbolUniqueId &&
	        isDirectChildPath(childPathUniqueId, metadata.parentPathUniqueId, sheetSymbolUniqueId) &&
	        metadata.sheetEntryName.toLowerCase() === portName.toLowerCase());
	}
	function isSheetEntry(label) {
	    return label?.labelType === 'sheet-entry';
	}
	function isDirectChildPath(childPathUniqueId, parentPathUniqueId, sheetSymbolUniqueId) {
	    return (childPathUniqueId === `${parentPathUniqueId}\\${sheetSymbolUniqueId}` ||
	        childPathUniqueId === `${parentPathUniqueId}/${sheetSymbolUniqueId}`);
	}
	function findPairedChildPort(entry, index) {
	    const metadata = entry.strategyMetadata?.['sheet-entry-metadata'];
	    if (!metadata) {
	        return undefined;
	    }
	    const ports = Array.from(index.labelNets.keys()).filter((label) => {
	        const portMetadata = label.strategyMetadata?.['port-metadata'];
	        const instance = index.labelInstances.get(label);
	        return (label.labelType === 'port' &&
	            portMetadata?.portName.toLowerCase() === metadata.sheetEntryName.toLowerCase() &&
	            instance?.sheetSymbol?.uniqueId === metadata.sheetSymbolUniqueId &&
	            portMetadata.pathUniqueId === instance.pathUniqueId);
	    });
	    return ports.length === 1 ? ports[0] : undefined;
	}
	function hasGenuineRelayContinuation(net, index) {
	    return (!net.netObjects.some((object) => object.connection) &&
	        !net.netObjects.some((object) => object.label && isNonSheetEntryNamingLabel(object.label) && object.label.labelType !== 'port') &&
	        net.netObjects
	            .map((object) => object.label)
	            .filter(isPlainScalarSheetEntry)
	            .some((entry) => findPairedChildPort(entry, index) !== undefined));
	}
	function getEligibilityIndex(group, hierarchyMode) {
	    let indexesByMode = eligibilityIndexes.get(group);
	    const existing = indexesByMode?.get(hierarchyMode);
	    if (existing?.mutationVersion === group.getMutationVersion()) {
	        return existing.index;
	    }
	    const index = {
	        labelNets: new Map(),
	        labelInstances: new Map(),
	        withPeer: {
	            sheetEntryEligibility: new Map(),
	            ineligibleUnscopedPairs: new Set(),
	            ineligibleRepeatedPairs: new Set(),
	        },
	        labelOnly: {
	            sheetEntryEligibility: new Map(),
	            ineligibleUnscopedPairs: new Set(),
	            ineligibleRepeatedPairs: new Set(),
	        },
	    };
	    for (const [instance, nets] of group.getNets()) {
	        for (const net of nets) {
	            for (const label of net.labels) {
	                index.labelNets.set(label, net);
	                index.labelInstances.set(label, instance);
	            }
	        }
	    }
	    for (const label of index.labelNets.keys()) {
	        if (label.labelType !== 'sheet-entry') {
	            continue;
	        }
	        const net = findNetContainingLabel(index, label);
	        const eligibleByLabel = net?.netObjects.some((object) => isNonSheetEntryNamingLabel(object.label)) ?? false;
	        const eligibleByPeer = eligibleByLabel || hasMatchingSheetEntryPeer(net, label, hierarchyMode);
	        index.labelOnly.sheetEntryEligibility.set(label, eligibleByLabel);
	        index.withPeer.sheetEntryEligibility.set(label, eligibleByPeer);
	        const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
	        if (!metadata) {
	            continue;
	        }
	        const pair = getPairKey(metadata.sheetSymbolUniqueId, metadata.sheetEntryName);
	        for (const [regime, isEligible] of [
	            [index.withPeer, eligibleByPeer],
	            [index.labelOnly, eligibleByLabel],
	        ]) {
	            if (isEligible) {
	                continue;
	            }
	            if (metadata.isRepeated) {
	                regime.ineligibleRepeatedPairs.add(getRepeatedPairKey(pair, metadata.channelIndex));
	            }
	            else {
	                regime.ineligibleUnscopedPairs.add(pair);
	            }
	        }
	    }
	    if (!indexesByMode) {
	        indexesByMode = new Map();
	        eligibilityIndexes.set(group, indexesByMode);
	    }
	    indexesByMode.set(hierarchyMode, { mutationVersion: group.getMutationVersion(), index });
	    return index;
	}
	function findNetContainingLabel(index, label) {
	    return index.labelNets.get(label);
	}
	function isNonSheetEntryNamingLabel(label) {
	    if (label?.labelType !== 'net-label' &&
	        label?.labelType !== 'power-port' &&
	        label?.labelType !== 'port' &&
	        label?.labelType !== 'off-sheet-connector') {
	        return false;
	    }
	    return (label.labelType !== 'port' ||
	        label.canNameNet ||
	        label.strategyMetadata?.harness?.harnessSource === 'synthetic-port');
	}
	function hasDifferentHierarchyIdentity(group, identity) {
	    const foldedIdentity = identity.toLowerCase();
	    return group
	        .getNetLabels()
	        .some((label) => (label.labelType === 'port' || label.labelType === 'sheet-entry') &&
	        label.originalLabel.toLowerCase() !== foldedIdentity);
	}
	function hasMatchingSheetEntryPeer(net, sheetEntryLabel, hierarchyMode) {
	    const metadata = sheetEntryLabel.strategyMetadata?.['sheet-entry-metadata'];
	    if (!net ||
	        !metadata ||
	        hierarchyMode !== altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical ||
	        metadata.isRepeated !== false ||
	        sheetEntryLabel.strategyMetadata?.harness ||
	        !net.netObjects.some((object) => object.wire) ||
	        net.netObjects.some((object) => object.connection)) {
	        return false;
	    }
	    const entryName = metadata.sheetEntryName;
	    return net.netObjects.some((object) => {
	        const candidate = object.label;
	        if (candidate === sheetEntryLabel || candidate?.labelType !== 'sheet-entry') {
	            return false;
	        }
	        const candidateMetadata = candidate.strategyMetadata?.['sheet-entry-metadata'];
	        return (candidateMetadata?.sheetEntryName === entryName &&
	            candidateMetadata.isRepeated === false &&
	            !candidate.strategyMetadata?.harness);
	    });
	}
	function getPairKey(sheetSymbolUniqueId, entryName) {
	    return `${sheetSymbolUniqueId}\u0000${entryName.toLowerCase()}`;
	}
	function getRepeatedPairKey(pair, channelIndex) {
	    return `${pair}\u0000${String(channelIndex)}`;
	}
	
	return AltiumSheetEntryNamingEligibility;
}

var hasRequiredAltiumHierarchyPriorityNamingRule;

function requireAltiumHierarchyPriorityNamingRule () {
	if (hasRequiredAltiumHierarchyPriorityNamingRule) return AltiumHierarchyPriorityNamingRule;
	hasRequiredAltiumHierarchyPriorityNamingRule = 1;
	Object.defineProperty(AltiumHierarchyPriorityNamingRule, "__esModule", { value: true });
	AltiumHierarchyPriorityNamingRule.AltiumHierarchyPriorityNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	const AltiumSheetEntryNamingEligibility_1 = requireAltiumSheetEntryNamingEligibility();
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const AltiumNetNameCaseSelection_1 = requireAltiumNetNameCaseSelection();
	const AltiumNetNamingStrategy_1 = requireAltiumNetNamingStrategy();
	class AltiumHierarchyPriorityNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        if (this.config.higherLevelNamesTakePriority !== altiumProjectFileTypes_1.NameNetsHierarchicallyMode.Yes) {
	            return undefined;
	        }
	        const candidates = this.collectAllNamingCandidates(group);
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        return this.selectBestCandidateWithHierarchyPriority(candidates, group);
	    }
	    collectAllNamingCandidates(group) {
	        const candidates = [];
	        const labelTypePriority = {
	            'power-port': 3,
	            'net-label': 1,
	            'off-sheet-connector': 4,
	            port: this.config.allowPortNetNames ? 5 : 999,
	            'sheet-entry': this.config.allowSheetEntryNetNames ? 6 : 999,
	        };
	        for (const [instance, nets] of group.getNets()) {
	            const hierarchyDepth = instance.path.length;
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    const isHierarchyLabel = label.labelType === 'port' || label.labelType === 'sheet-entry';
	                    const isSyntheticHarnessHierarchyLabel = isHierarchyLabel && label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-');
	                    const isAliasedSyntheticNetLabel = label.strategyMetadata?.harness?.harnessSource === 'synthetic-net-label' &&
	                        label.strategyMetadata.harness.aliasedFromRegularLabel === true;
	                    const canContributeName = !isSyntheticHarnessHierarchyLabel && !isAliasedSyntheticNetLabel && label.canNameNet;
	                    if (!canContributeName) {
	                        continue;
	                    }
	                    const isNamingEligible = label.labelType === 'sheet-entry' ? (0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false)
	                        : label.labelType === 'port' ?
	                            (0, AltiumSheetEntryNamingEligibility_1.isAltiumPairedChildPortNamingEligible)(group, label, this.config.mode, false, true)
	                            : true;
	                    if (!isNamingEligible) {
	                        continue;
	                    }
	                    candidates.push({
	                        name: label.formattedLabel ?? label.originalLabel,
	                        labelType: label.labelType,
	                        hierarchyDepth,
	                        priority: labelTypePriority[label.labelType] ?? 999,
	                        label,
	                        net,
	                        instance,
	                    });
	                }
	            }
	        }
	        const hasOrdinaryNetLabelCandidate = candidates.some((candidate) => this.isAuthoredNetLabel(candidate));
	        const ordinaryCandidateNamesByNet = new Map();
	        for (const candidate of candidates) {
	            if (!this.isAuthoredNetLabel(candidate)) {
	                continue;
	            }
	            const names = ordinaryCandidateNamesByNet.get(candidate.net) ?? new Set();
	            names.add(candidate.label.originalLabel.toLowerCase());
	            ordinaryCandidateNamesByNet.set(candidate.net, names);
	        }
	        return candidates.map((candidate) => {
	            const isDemotableHarnessSynthetic = this.isHarnessSynthetic(candidate) &&
	                this.hasRenamingOrdinaryCandidate(candidate, ordinaryCandidateNamesByNet) &&
	                !(0, AltiumNetNamingStrategy_1.appliesChannelNetFormatting)(candidate.instance);
	            return {
	                ...candidate,
	                powerPortRank: this.getPowerPortRank(candidate, hasOrdinaryNetLabelCandidate),
	                hierarchyLabelRank: candidate.labelType === 'port' || candidate.labelType === 'sheet-entry' ? 1 : 0,
	                busRangeNetLabelMemberRank: candidate.labelType === 'net-label' && candidate.label.strategyMetadata?.busRangeNetLabelMember === true ?
	                    0
	                    : 1,
	                harnessSyntheticRank: isDemotableHarnessSynthetic ? 1 : 0,
	                caseFoldedName: candidate.name.toLowerCase(),
	                stableIdentity: this.getStableCandidateIdentity(candidate),
	            };
	        });
	    }
	    getPowerPortRank(candidate, hasOrdinaryNetLabelCandidate) {
	        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.Yes) {
	            return candidate.labelType === 'power-port' ? 0 : 1;
	        }
	        const isDemotedByOrdinaryNetLabel = hasOrdinaryNetLabelCandidate &&
	            (candidate.labelType === 'power-port' || candidate.labelType === 'off-sheet-connector');
	        return isDemotedByOrdinaryNetLabel ? 1 : 0;
	    }
	    hasRenamingOrdinaryCandidate(candidate, ordinaryCandidateNamesByNet) {
	        const ordinaryNames = ordinaryCandidateNamesByNet.get(candidate.net);
	        if (!ordinaryNames) {
	            return false;
	        }
	        const harness = candidate.label.strategyMetadata?.harness;
	        const memberName = harness?.harnessMemberName?.toLowerCase();
	        if (harness?.bundleNamedByCarrierLabel !== true || memberName === undefined) {
	            return true;
	        }
	        return Array.from(ordinaryNames).some((name) => name !== memberName);
	    }
	    selectBestCandidateWithHierarchyPriority(candidates, group) {
	        if (candidates.length === 0) {
	            return 'NET_UNKNOWN';
	        }
	        candidates.sort((a, b) => {
	            return (a.powerPortRank - b.powerPortRank ||
	                a.hierarchyLabelRank - b.hierarchyLabelRank ||
	                a.hierarchyDepth - b.hierarchyDepth ||
	                a.priority - b.priority ||
	                a.busRangeNetLabelMemberRank - b.busRangeNetLabelMemberRank ||
	                a.harnessSyntheticRank - b.harnessSyntheticRank ||
	                sigma_engine_shared_1.Utils.naturalStringSort(a.caseFoldedName, b.caseFoldedName) ||
	                this.compareStrings(a.caseFoldedName, b.caseFoldedName) ||
	                (0, AltiumNetNameCaseSelection_1.compareAltiumNetNameCaseVariants)(a.name, b.name) ||
	                this.compareStrings(a.stableIdentity, b.stableIdentity));
	        });
	        const winner = candidates[0];
	        if (winner.labelType === 'sheet-entry') {
	            const channelPortName = this.resolveSheetEntryThroughChildPort(winner.label, group);
	            if (channelPortName !== undefined) {
	                return channelPortName;
	            }
	        }
	        const winnerName = winner.labelType === 'net-label' ?
	            (this.namingStrategy.getHierarchicalNetLabelName?.(winner.label, winner.net, winner.instance, group) ??
	                winner.name)
	            : winner.name;
	        return this.dereferenceLabelName(winnerName);
	    }
	    isHarnessSynthetic(candidate) {
	        return candidate.label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-') ?? false;
	    }
	    isAuthoredNetLabel(candidate) {
	        const metadata = candidate.label.strategyMetadata;
	        return (candidate.labelType === 'net-label' &&
	            !this.isHarnessSynthetic(candidate) &&
	            (metadata?.busRangeMember !== true || metadata.busRangeNetLabelMember === true));
	    }
	    getStableCandidateIdentity(candidate) {
	        const sheetEntryMetadata = candidate.label.strategyMetadata?.['sheet-entry-metadata'];
	        const portMetadata = candidate.label.strategyMetadata?.['port-metadata'];
	        return [
	            candidate.instance.pathUniqueId,
	            candidate.labelType,
	            candidate.label.originalLabel,
	            candidate.label.position[0],
	            candidate.label.position[1],
	            candidate.label.strategyMetadata?.harness?.harnessSource ?? '',
	            sheetEntryMetadata?.sheetSymbolUniqueId ?? '',
	            portMetadata?.pathUniqueId ?? '',
	        ].join('\0');
	    }
	    compareStrings(a, b) {
	        return (a === b ? 0
	            : a < b ? -1
	                : 1);
	    }
	    resolveSheetEntryThroughChildPort(sheetEntryLabel, group) {
	        const sheetEntryMetadata = sheetEntryLabel.strategyMetadata?.['sheet-entry-metadata'];
	        if (!sheetEntryMetadata) {
	            return undefined;
	        }
	        const entryNameLower = sheetEntryMetadata.sheetEntryName.toLowerCase();
	        const portNames = [];
	        for (const [instance, nets] of group.getNets()) {
	            if (instance.sheetSymbol?.uniqueId !== sheetEntryMetadata.sheetSymbolUniqueId) {
	                continue;
	            }
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType !== 'port' || !label.formattedLabel) {
	                        continue;
	                    }
	                    const portMetadata = label.strategyMetadata?.['port-metadata'];
	                    if (!portMetadata ||
	                        portMetadata.pathUniqueId !== instance.pathUniqueId ||
	                        portMetadata.portName.toLowerCase() !== entryNameLower) {
	                        continue;
	                    }
	                    if (sheetEntryMetadata.isRepeated &&
	                        sheetEntryMetadata.channelIndex !== undefined &&
	                        portMetadata.channelIndex !== sheetEntryMetadata.channelIndex) {
	                        continue;
	                    }
	                    const derefed = this.dereferenceLabelName(label.formattedLabel);
	                    if (derefed !== this.dereferenceLabelName(label.originalLabel)) {
	                        portNames.push(derefed);
	                    }
	                }
	            }
	        }
	        if (portNames.length === 0) {
	            return undefined;
	        }
	        portNames.sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	        return portNames[0];
	    }
	}
	AltiumHierarchyPriorityNamingRule.AltiumHierarchyPriorityNamingRule = AltiumHierarchyPriorityNamingRule$1;
	
	return AltiumHierarchyPriorityNamingRule;
}

var AltiumPowerPortNamingRule = {};

var hasRequiredAltiumPowerPortNamingRule;

function requireAltiumPowerPortNamingRule () {
	if (hasRequiredAltiumPowerPortNamingRule) return AltiumPowerPortNamingRule;
	hasRequiredAltiumPowerPortNamingRule = 1;
	Object.defineProperty(AltiumPowerPortNamingRule, "__esModule", { value: true });
	AltiumPowerPortNamingRule.AltiumPowerPortNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	const AltiumNetNameCaseSelection_1 = requireAltiumNetNameCaseSelection();
	class AltiumPowerPortNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    constructor(config, namingStrategy) {
	        super(config, namingStrategy);
	    }
	    apply(group, _hierarchy) {
	        if (!group.hasLabelType('power-port')) {
	            return undefined;
	        }
	        const powerPortNames = [];
	        const powerPortNamesByFold = new Map();
	        for (const label of group.getNetLabels()) {
	            if (label.labelType !== 'power-port') {
	                continue;
	            }
	            const name = label.formattedLabel ?? label.originalLabel;
	            const fold = name.toLowerCase();
	            const existingName = powerPortNamesByFold.get(fold);
	            if (existingName === undefined || (0, AltiumNetNameCaseSelection_1.compareAltiumNetNameCaseVariants)(name, existingName) < 0) {
	                powerPortNamesByFold.set(fold, name);
	            }
	            powerPortNames.push(name);
	        }
	        if (powerPortNames.length === 0) {
	            return undefined;
	        }
	        powerPortNames.sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	        return this.dereferenceLabelName(powerPortNamesByFold.get(powerPortNames[0].toLowerCase()));
	    }
	}
	AltiumPowerPortNamingRule.AltiumPowerPortNamingRule = AltiumPowerPortNamingRule$1;
	
	return AltiumPowerPortNamingRule;
}

var AltiumNetLabelNamingRule = {};

var hasRequiredAltiumNetLabelNamingRule;

function requireAltiumNetLabelNamingRule () {
	if (hasRequiredAltiumNetLabelNamingRule) return AltiumNetLabelNamingRule;
	hasRequiredAltiumNetLabelNamingRule = 1;
	Object.defineProperty(AltiumNetLabelNamingRule, "__esModule", { value: true });
	AltiumNetLabelNamingRule.AltiumNetLabelNamingRule = void 0;
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	const AltiumEnums_1 = requireAltiumEnums();
	const AltiumSheetEntryNamingEligibility_1 = requireAltiumSheetEntryNamingEligibility();
	const AltiumNetNameCaseSelection_1 = requireAltiumNetNameCaseSelection();
	function compareLabelNamesOrdinal(a, b) {
	    const foldedA = a.toLowerCase();
	    const foldedB = b.toLowerCase();
	    let aIndex = 0;
	    let bIndex = 0;
	    while (aIndex < foldedA.length && bIndex < foldedB.length) {
	        const aCharacter = foldedA[aIndex];
	        const bCharacter = foldedB[bIndex];
	        const aIsDigit = aCharacter >= '0' && aCharacter <= '9';
	        const bIsDigit = bCharacter >= '0' && bCharacter <= '9';
	        if (aIsDigit && bIsDigit) {
	            let aRunEnd = aIndex + 1;
	            let bRunEnd = bIndex + 1;
	            while (aRunEnd < foldedA.length && foldedA[aRunEnd] >= '0' && foldedA[aRunEnd] <= '9') {
	                aRunEnd++;
	            }
	            while (bRunEnd < foldedB.length && foldedB[bRunEnd] >= '0' && foldedB[bRunEnd] <= '9') {
	                bRunEnd++;
	            }
	            let aSignificantStart = aIndex;
	            let bSignificantStart = bIndex;
	            while (aSignificantStart < aRunEnd - 1 && foldedA[aSignificantStart] === '0') {
	                aSignificantStart++;
	            }
	            while (bSignificantStart < bRunEnd - 1 && foldedB[bSignificantStart] === '0') {
	                bSignificantStart++;
	            }
	            const aSignificantLength = aRunEnd - aSignificantStart;
	            const bSignificantLength = bRunEnd - bSignificantStart;
	            if (aSignificantLength !== bSignificantLength) {
	                return aSignificantLength - bSignificantLength;
	            }
	            for (let digitIndex = 0; digitIndex < aSignificantLength; digitIndex++) {
	                const aDigit = foldedA[aSignificantStart + digitIndex];
	                const bDigit = foldedB[bSignificantStart + digitIndex];
	                if (aDigit !== bDigit) {
	                    return aDigit < bDigit ? -1 : 1;
	                }
	            }
	            const aRunLength = aRunEnd - aIndex;
	            const bRunLength = bRunEnd - bIndex;
	            if (aRunLength !== bRunLength) {
	                return aRunLength - bRunLength;
	            }
	            aIndex = aRunEnd;
	            bIndex = bRunEnd;
	            continue;
	        }
	        if (aCharacter !== bCharacter) {
	            return aCharacter < bCharacter ? -1 : 1;
	        }
	        aIndex++;
	        bIndex++;
	    }
	    if (aIndex !== foldedA.length || bIndex !== foldedB.length) {
	        return aIndex === foldedA.length ? -1 : 1;
	    }
	    return (a === b ? 0
	        : a < b ? -1
	            : 1);
	}
	class AltiumNetLabelNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        const canUseSheetEntries = this.config.allowSheetEntryNetNames && !this.config.allowPortNetNames;
	        if (!group.hasLabelType('net-label') && (!canUseSheetEntries || !group.hasLabelType('sheet-entry'))) {
	            return undefined;
	        }
	        const candidates = [];
	        const netLabelByFold = new Map();
	        const sheetEntryByFold = new Map();
	        const busMemberFolds = new Set();
	        for (const [instance, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType === 'net-label' && label.strategyMetadata?.busRangeNetLabelMember) {
	                        busMemberFolds.add(label.originalLabel.toLowerCase());
	                    }
	                    if (label.labelType === 'net-label') {
	                        if (!label.formattedLabel) {
	                            continue;
	                        }
	                        const name = this.dereferenceLabelName(this.namingStrategy.getNetLabelNameForNamingGroup(label, instance, group));
	                        this.foldName(netLabelByFold, name);
	                        candidates.push({ name, isHarnessSynthetic: this.isHarnessSynthetic(label), labelType: 'net-label' });
	                        continue;
	                    }
	                    const isSyntheticHarnessEntry = label.labelType === 'sheet-entry' &&
	                        label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-');
	                    if (canUseSheetEntries &&
	                        label.labelType === 'sheet-entry' &&
	                        !isSyntheticHarnessEntry &&
	                        this.canUseSheetEntryName(group, label)) {
	                        const name = this.dereferenceLabelName(label.formattedLabel ?? label.originalLabel);
	                        this.foldName(sheetEntryByFold, name);
	                        candidates.push({ name, isHarnessSynthetic: this.isHarnessSynthetic(label), labelType: 'sheet-entry' });
	                    }
	                }
	            }
	        }
	        for (const [_instance, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType !== 'net-label' || !label.formattedLabel) {
	                        continue;
	                    }
	                    const formatted = this.dereferenceLabelName(label.formattedLabel);
	                    const original = this.dereferenceLabelName(label.originalLabel);
	                    if (formatted === original) {
	                        continue;
	                    }
	                    if (netLabelByFold.has(original.toLowerCase())) {
	                        netLabelByFold.delete(formatted.toLowerCase());
	                        for (let i = candidates.length - 1; i >= 0; i--) {
	                            if (candidates[i].name === formatted) {
	                                candidates.splice(i, 1);
	                            }
	                        }
	                    }
	                }
	            }
	        }
	        const netLabelNames = new Set(netLabelByFold.values());
	        const sheetEntryNames = new Set(Array.from(sheetEntryByFold, ([fold, name]) => netLabelByFold.get(fold) ?? name));
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        if (netLabelNames.size === 0 && group.hasLabelType('power-port')) {
	            return undefined;
	        }
	        if (sheetEntryNames.size > 1) {
	            const sharedNetLabelNames = Array.from(netLabelNames)
	                .filter((name) => sheetEntryNames.has(name))
	                .sort(compareLabelNamesOrdinal);
	            if (sharedNetLabelNames.length > 0) {
	                return sharedNetLabelNames[0];
	            }
	        }
	        const differentialNetLabelNames = Array.from(netLabelNames)
	            .filter((name) => this.hasSheetEntryWithoutSeparatedPolaritySuffix(name, sheetEntryNames))
	            .sort(compareLabelNamesOrdinal);
	        if (differentialNetLabelNames.length > 0) {
	            return differentialNetLabelNames[0];
	        }
	        const normalizedCandidates = candidates.map((candidate) => ({
	            ...candidate,
	            name: candidate.labelType === 'sheet-entry' ?
	                (netLabelByFold.get(candidate.name.toLowerCase()) ??
	                    sheetEntryByFold.get(candidate.name.toLowerCase()) ??
	                    candidate.name)
	                : (netLabelByFold.get(candidate.name.toLowerCase()) ?? candidate.name),
	        }));
	        const demotedHarnessSyntheticCandidates = this.getDemotedHarnessSyntheticCandidates(normalizedCandidates);
	        const sorted = normalizedCandidates.sort((a, b) => {
	            const aBusMember = busMemberFolds.has(a.name.toLowerCase()) ? 0 : 1;
	            const bBusMember = busMemberFolds.has(b.name.toLowerCase()) ? 0 : 1;
	            if (aBusMember !== bBusMember) {
	                return aBusMember - bBusMember;
	            }
	            const aEntry = a.labelType === 'sheet-entry' ? 1 : 0;
	            const bEntry = b.labelType === 'sheet-entry' ? 1 : 0;
	            if (aEntry !== bEntry) {
	                return aEntry - bEntry;
	            }
	            const aDemoted = demotedHarnessSyntheticCandidates.has(a) ? 1 : 0;
	            const bDemoted = demotedHarnessSyntheticCandidates.has(b) ? 1 : 0;
	            if (aDemoted !== bDemoted) {
	                return aDemoted - bDemoted;
	            }
	            return compareLabelNamesOrdinal(a.name, b.name);
	        });
	        return sorted[0].name;
	    }
	    getDemotedHarnessSyntheticCandidates(candidates) {
	        const demotedCandidates = new Set();
	        for (const candidate of candidates) {
	            const segments = candidate.name.split('.');
	            if (!candidate.isHarnessSynthetic || segments.length < 2) {
	                continue;
	            }
	            const root = segments[0].toLowerCase();
	            const hasHigherPrioritySameRootCandidate = candidates.some((otherCandidate) => {
	                const otherSegments = otherCandidate.name.split('.');
	                return (otherCandidate.labelType === 'net-label' &&
	                    otherCandidate.name !== candidate.name &&
	                    otherSegments.length >= 2 &&
	                    otherSegments[0].toLowerCase() === root &&
	                    (!otherCandidate.isHarnessSynthetic || otherSegments.length < segments.length));
	            });
	            if (hasHigherPrioritySameRootCandidate) {
	                demotedCandidates.add(candidate);
	            }
	        }
	        return demotedCandidates;
	    }
	    foldName(byFold, name) {
	        const fold = name.toLowerCase();
	        const existing = byFold.get(fold);
	        byFold.set(fold, !existing || (0, AltiumNetNameCaseSelection_1.compareAltiumNetNameCaseVariants)(name, existing) < 0 ? name : existing);
	    }
	    isHarnessSynthetic(label) {
	        return label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-') ?? false;
	    }
	    hasSheetEntryWithoutSeparatedPolaritySuffix(name, sheetEntryNames) {
	        if (!/_([PN])$/i.test(name)) {
	            return false;
	        }
	        return sheetEntryNames.has(name.replace(/_([PN])$/i, '$1'));
	    }
	    canUseSheetEntryName(group, label) {
	        const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
	        if (!metadata) {
	            return (0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false);
	        }
	        return ((0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false) &&
	            !metadata.isRepeated &&
	            metadata.channelIndexes.length <= 1 &&
	            metadata.ioType !== AltiumEnums_1.PortIOType.Input);
	    }
	}
	AltiumNetLabelNamingRule.AltiumNetLabelNamingRule = AltiumNetLabelNamingRule$1;
	
	return AltiumNetLabelNamingRule;
}

var AltiumOffSheetConnectorNamingRule = {};

var hasRequiredAltiumOffSheetConnectorNamingRule;

function requireAltiumOffSheetConnectorNamingRule () {
	if (hasRequiredAltiumOffSheetConnectorNamingRule) return AltiumOffSheetConnectorNamingRule;
	hasRequiredAltiumOffSheetConnectorNamingRule = 1;
	Object.defineProperty(AltiumOffSheetConnectorNamingRule, "__esModule", { value: true });
	AltiumOffSheetConnectorNamingRule.AltiumOffSheetConnectorNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	class AltiumOffSheetConnectorNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        if (!group.hasLabelType('off-sheet-connector')) {
	            return undefined;
	        }
	        const offSheetConnectorNames = [];
	        for (const label of group.getNetLabels()) {
	            if (label.labelType === 'off-sheet-connector') {
	                if (!label.formattedLabel) {
	                    continue;
	                }
	                offSheetConnectorNames.push(label.formattedLabel);
	            }
	        }
	        if (offSheetConnectorNames.length === 0) {
	            return undefined;
	        }
	        offSheetConnectorNames.sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	        return this.dereferenceLabelName(offSheetConnectorNames[0]);
	    }
	}
	AltiumOffSheetConnectorNamingRule.AltiumOffSheetConnectorNamingRule = AltiumOffSheetConnectorNamingRule$1;
	
	return AltiumOffSheetConnectorNamingRule;
}

var AltiumPortNamingRule = {};

var hasRequiredAltiumPortNamingRule;

function requireAltiumPortNamingRule () {
	if (hasRequiredAltiumPortNamingRule) return AltiumPortNamingRule;
	hasRequiredAltiumPortNamingRule = 1;
	Object.defineProperty(AltiumPortNamingRule, "__esModule", { value: true });
	AltiumPortNamingRule.AltiumPortNamingRule = void 0;
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	const AltiumNetNameCaseSelection_1 = requireAltiumNetNameCaseSelection();
	const AltiumNetNamingStrategy_1 = requireAltiumNetNamingStrategy();
	class AltiumPortNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        if (!this.config.allowPortNetNames) {
	            return undefined;
	        }
	        if (!group.hasLabelType('port')) {
	            return undefined;
	        }
	        const portNames = [];
	        for (const [instance, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType !== 'port') {
	                        continue;
	                    }
	                    const name = (0, AltiumNetNamingStrategy_1.appliesChannelNetFormatting)(instance) && label.formattedLabel ? label.formattedLabel : label.originalLabel;
	                    portNames.push(this.dereferenceLabelName(name));
	                }
	            }
	        }
	        if (portNames.length === 0) {
	            return undefined;
	        }
	        portNames.sort(AltiumNetNameCaseSelection_1.compareAltiumNetNameCandidates);
	        return this.dereferenceLabelName(portNames[0]);
	    }
	}
	AltiumPortNamingRule.AltiumPortNamingRule = AltiumPortNamingRule$1;
	
	return AltiumPortNamingRule;
}

var AltiumSheetEntryNamingRule = {};

var hasRequiredAltiumSheetEntryNamingRule;

function requireAltiumSheetEntryNamingRule () {
	if (hasRequiredAltiumSheetEntryNamingRule) return AltiumSheetEntryNamingRule;
	hasRequiredAltiumSheetEntryNamingRule = 1;
	Object.defineProperty(AltiumSheetEntryNamingRule, "__esModule", { value: true });
	AltiumSheetEntryNamingRule.AltiumSheetEntryNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	const AltiumEnums_1 = requireAltiumEnums();
	const AltiumSheetEntryNamingEligibility_1 = requireAltiumSheetEntryNamingEligibility();
	class AltiumSheetEntryNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        if (!this.config.allowSheetEntryNetNames) {
	            return undefined;
	        }
	        if (!group.hasLabelType('sheet-entry')) {
	            return undefined;
	        }
	        if (!group.hasLabelType('port')) {
	            return undefined;
	        }
	        const sheetEntryNames = [];
	        for (const label of group.getNetLabels()) {
	            if (label.labelType === 'sheet-entry' && this.canUseSheetEntryName(group, label)) {
	                sheetEntryNames.push(label.formattedLabel ?? label.originalLabel);
	            }
	        }
	        if (sheetEntryNames.length === 0) {
	            return undefined;
	        }
	        sheetEntryNames.sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	        return this.dereferenceLabelName(sheetEntryNames[0]);
	    }
	    canUseSheetEntryName(group, label) {
	        const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
	        if (!metadata) {
	            return (0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false);
	        }
	        return ((0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false) &&
	            !metadata.isRepeated &&
	            metadata.channelIndexes.length <= 1 &&
	            metadata.ioType !== AltiumEnums_1.PortIOType.Input);
	    }
	}
	AltiumSheetEntryNamingRule.AltiumSheetEntryNamingRule = AltiumSheetEntryNamingRule$1;
	
	return AltiumSheetEntryNamingRule;
}

var AltiumNamingPinNamingRule = {};

var hasRequiredAltiumNamingPinNamingRule;

function requireAltiumNamingPinNamingRule () {
	if (hasRequiredAltiumNamingPinNamingRule) return AltiumNamingPinNamingRule;
	hasRequiredAltiumNamingPinNamingRule = 1;
	Object.defineProperty(AltiumNamingPinNamingRule, "__esModule", { value: true });
	AltiumNamingPinNamingRule.AltiumNamingPinNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	class AltiumNamingPinNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        const namingPinNames = [];
	        for (const [_instance, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    if (conn.connection.appliesNetName) {
	                        namingPinNames.push(conn.connection.appliesNetName.label);
	                    }
	                }
	            }
	        }
	        if (namingPinNames.length === 0) {
	            return undefined;
	        }
	        namingPinNames.sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	        return this.dereferenceLabelName(namingPinNames[0]);
	    }
	}
	AltiumNamingPinNamingRule.AltiumNamingPinNamingRule = AltiumNamingPinNamingRule$1;
	
	return AltiumNamingPinNamingRule;
}

var AltiumComponentConnectionNamingRule = {};

var compareAltiumPinReferences = {};

var hasRequiredCompareAltiumPinReferences;

function requireCompareAltiumPinReferences () {
	if (hasRequiredCompareAltiumPinReferences) return compareAltiumPinReferences;
	hasRequiredCompareAltiumPinReferences = 1;
	Object.defineProperty(compareAltiumPinReferences, "__esModule", { value: true });
	compareAltiumPinReferences.compareAltiumPinReferences = compareAltiumPinReferences$1;
	const sigma_engine_shared_1 = requireDist$1();
	function compareAltiumPinReferences$1(left, right) {
	    const leftIsNumeric = /^\d+$/.test(left);
	    const rightIsNumeric = /^\d+$/.test(right);
	    if (leftIsNumeric !== rightIsNumeric) {
	        return leftIsNumeric ? -1 : 1;
	    }
	    return sigma_engine_shared_1.Utils.naturalStringSort(left, right);
	}
	
	return compareAltiumPinReferences;
}

var hasRequiredAltiumComponentConnectionNamingRule;

function requireAltiumComponentConnectionNamingRule () {
	if (hasRequiredAltiumComponentConnectionNamingRule) return AltiumComponentConnectionNamingRule;
	hasRequiredAltiumComponentConnectionNamingRule = 1;
	Object.defineProperty(AltiumComponentConnectionNamingRule, "__esModule", { value: true });
	AltiumComponentConnectionNamingRule.AltiumComponentConnectionNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumNetNamingRule_1 = requireAltiumNetNamingRule();
	const project_1 = requireProject();
	const compareAltiumPinReferences_1 = requireCompareAltiumPinReferences();
	class AltiumComponentConnectionNamingRule$1 extends AltiumNetNamingRule_1.AltiumNetNamingRule {
	    apply(group, _hierarchy) {
	        const hierarchicalNaming = this.config.higherLevelNamesTakePriority === project_1.NameNetsHierarchicallyMode.Yes;
	        const connections = [];
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    connections.push({
	                        moduleName: conn.connection.moduleName,
	                        pinRef: conn.connection.pinRef,
	                        depth,
	                    });
	                }
	            }
	        }
	        if (connections.length === 0) {
	            return undefined;
	        }
	        connections.sort((a, b) => {
	            if (hierarchicalNaming && a.depth !== b.depth) {
	                return a.depth - b.depth;
	            }
	            const moduleCompare = sigma_engine_shared_1.Utils.naturalStringSort(a.moduleName, b.moduleName);
	            if (moduleCompare !== 0) {
	                return moduleCompare;
	            }
	            return (0, compareAltiumPinReferences_1.compareAltiumPinReferences)(a.pinRef, b.pinRef);
	        });
	        const firstConn = connections[0];
	        return `Net${firstConn.moduleName}_${firstConn.pinRef}`;
	    }
	}
	AltiumComponentConnectionNamingRule.AltiumComponentConnectionNamingRule = AltiumComponentConnectionNamingRule$1;
	
	return AltiumComponentConnectionNamingRule;
}

var AltiumSheetEntryLocalRepeatLocalNamingRule = {};

var AltiumNetLocalNamingRule = {};

var hasRequiredAltiumNetLocalNamingRule;

function requireAltiumNetLocalNamingRule () {
	if (hasRequiredAltiumNetLocalNamingRule) return AltiumNetLocalNamingRule;
	hasRequiredAltiumNetLocalNamingRule = 1;
	Object.defineProperty(AltiumNetLocalNamingRule, "__esModule", { value: true });
	AltiumNetLocalNamingRule.AltiumNetLocalNamingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class AltiumNetLocalNamingRule$1 {
	    constructor(config, namingStrategy) {
	        this.config = config;
	        this.namingStrategy = namingStrategy;
	        this.type = NetNamingRules_1.NetNamingRuleType.LocalNaming;
	    }
	}
	AltiumNetLocalNamingRule.AltiumNetLocalNamingRule = AltiumNetLocalNamingRule$1;
	
	return AltiumNetLocalNamingRule;
}

var hasRequiredAltiumSheetEntryLocalRepeatLocalNamingRule;

function requireAltiumSheetEntryLocalRepeatLocalNamingRule () {
	if (hasRequiredAltiumSheetEntryLocalRepeatLocalNamingRule) return AltiumSheetEntryLocalRepeatLocalNamingRule;
	hasRequiredAltiumSheetEntryLocalRepeatLocalNamingRule = 1;
	Object.defineProperty(AltiumSheetEntryLocalRepeatLocalNamingRule, "__esModule", { value: true });
	AltiumSheetEntryLocalRepeatLocalNamingRule.AltiumSheetEntryLocalRepeatLocalNamingRule = void 0;
	const _ = requireLodash();
	const AltiumLabelTypes_1 = requireAltiumLabelTypes();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	const AltiumNetLocalNamingRule_1 = requireAltiumNetLocalNamingRule();
	class AltiumSheetEntryLocalRepeatLocalNamingRule$1 extends AltiumNetLocalNamingRule_1.AltiumNetLocalNamingRule {
	    apply(hierarchy) {
	        if (!this.config.allowSheetEntryNetNames) {
	            return;
	        }
	        this.recombobulateRepeatedSheetEntries(hierarchy);
	    }
	    recombobulateRepeatedSheetEntries(hierarchy) {
	        const repeatSheetEntries = [];
	        for (const obj of hierarchy.combobulator.defaultDimension.iterateLabelObjects()) {
	            if (obj.label?.labelType === 'sheet-entry' && obj.label.strategyMetadata?.['sheet-entry-metadata']?.isRepeated) {
	                repeatSheetEntries.push(obj);
	            }
	        }
	        for (const repeatSheetEntry of repeatSheetEntries) {
	            const labelStrategyMetadata = repeatSheetEntry.label?.strategyMetadata?.['sheet-entry-metadata'];
	            if (!labelStrategyMetadata) {
	                throw new Error(`Sheet entry label is missing metadata`);
	            }
	            const endpoints = Array.from(new Set(repeatSheetEntry.endpoints.map((e) => e.dimension)));
	            if (endpoints.length > 1) {
	                throw new Error(`Repeat sheet entry ${repeatSheetEntry.label?.formattedLabel} is connected to multiple dimensions`);
	            }
	            const dimension = hierarchy.combobulator.getDimension(endpoints[0]);
	            const connectedObjs = Array.from(dimension.getObjectsConnectedToPosition(repeatSheetEntry.endpoints[0].position));
	            const connectedLabelNetObjects = connectedObjs.filter((o) => o.label?.labelType === 'net-label');
	            const useLabelNetObject = connectedLabelNetObjects[0];
	            if (!useLabelNetObject?.label) {
	                continue;
	            }
	            if (connectedLabelNetObjects.length > 1) {
	                const uniqueLabels = new Set(connectedLabelNetObjects.map((o) => o.label?.formattedLabel));
	                if (uniqueLabels.size > 1) {
	                    throw new Error(`Repeat sheet entry ${repeatSheetEntry.label?.formattedLabel} is connected to multiple labels: ${Array.from(uniqueLabels).join(', ')}`);
	                }
	            }
	            const netLabel = useLabelNetObject.label;
	            let parsedName;
	            try {
	                parsedName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.parseLabelTypeName(netLabel.originalLabel);
	            }
	            catch (e) {
	                if (e && e instanceof Error && e.message !== 'Not a label type name string') {
	                    throw e;
	                }
	                parsedName = {
	                    labelType: 'net-label',
	                    name: netLabel.originalLabel,
	                    argument: undefined,
	                };
	            }
	            if (!connectedObjs.some((connectedObj) => connectedObj === repeatSheetEntry)) {
	                throw new Error(`Repeat sheet entry ${repeatSheetEntry.label?.formattedLabel} is not connected to itself`);
	            }
	            for (const obj of connectedObjs) {
	                dimension.removeObject(obj);
	            }
	            const busMembersByChannel = AltiumSheetEntryLocalRepeatLocalNamingRule$1.getTappedBusRangeMembersByNumericIdentity(connectedObjs, labelStrategyMetadata.sheetEntryName);
	            for (const channelIndex of labelStrategyMetadata.channelIndexes) {
	                const memberName = busMembersByChannel.get(channelIndex);
	                const rawParsedName = memberName ?? `${parsedName.name}${channelIndex}`;
	                const newLabelName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(parsedName.labelType, rawParsedName, parsedName.argument);
	                const newDimension = hierarchy.combobulator.getOrCreateDimension((0, AltiumLabelTypes_1.nameAltiumBusDimension)(parsedName.name, rawParsedName));
	                for (let obj of connectedObjs) {
	                    obj = {
	                        ...structuredClone(_.omit(obj, 'net', 'netRef')),
	                        net: undefined,
	                        netRef: hierarchy.combobulator.getUniqueNetRef(newLabelName),
	                    };
	                    if (obj.wire) {
	                        newDimension.addWireObject({
	                            ...obj,
	                            endpoints: obj.endpoints.map((e) => ({ ...e, dimension: newDimension.name })),
	                        });
	                    }
	                    if (obj.label) {
	                        if (obj.appliesNetName === (obj.label.formattedLabel ?? obj.label.originalLabel)) {
	                            obj.appliesNetName = newLabelName;
	                            obj.label.formattedLabel = newLabelName;
	                        }
	                        newDimension.addPoint(obj.label.position, obj);
	                        if (obj.label.strategyMetadata?.['sheet-entry-metadata']) {
	                            obj.label.strategyMetadata['sheet-entry-metadata'].channelIndex = channelIndex;
	                        }
	                    }
	                    if (obj.connection) {
	                        throw new Error(`Connections not yet supported in sheet entry repeat rule`);
	                    }
	                }
	                if (memberName !== undefined) {
	                    hierarchy.combobulator.setLabel([{ position: repeatSheetEntry.endpoints[0].position, dimension: newDimension.name }], memberName, 'net-label', { busRangeMember: true });
	                }
	            }
	        }
	        for (const child of hierarchy.children) {
	            this.recombobulateRepeatedSheetEntries(child);
	        }
	    }
	    static getTappedBusRangeMembersByNumericIdentity(connectedObjs, entryName) {
	        const members = new Map();
	        for (const obj of connectedObjs) {
	            for (const member of obj.wire?.strategyMetadata?.busTapMembers ?? []) {
	                const memberIndex = /(\d+)$/.exec(member)?.[1];
	                if (memberIndex === undefined) {
	                    continue;
	                }
	                const channelIndex = Number(memberIndex);
	                if (member.toLowerCase() !== `${entryName}${channelIndex}`.toLowerCase()) {
	                    continue;
	                }
	                const existing = members.get(channelIndex);
	                if (existing === undefined) {
	                    members.set(channelIndex, member);
	                }
	            }
	        }
	        return members;
	    }
	}
	AltiumSheetEntryLocalRepeatLocalNamingRule.AltiumSheetEntryLocalRepeatLocalNamingRule = AltiumSheetEntryLocalRepeatLocalNamingRule$1;
	
	return AltiumSheetEntryLocalRepeatLocalNamingRule;
}

var AltiumPowerPortLocalNamingRule = {};

var hasRequiredAltiumPowerPortLocalNamingRule;

function requireAltiumPowerPortLocalNamingRule () {
	if (hasRequiredAltiumPowerPortLocalNamingRule) return AltiumPowerPortLocalNamingRule;
	hasRequiredAltiumPowerPortLocalNamingRule = 1;
	Object.defineProperty(AltiumPowerPortLocalNamingRule, "__esModule", { value: true });
	AltiumPowerPortLocalNamingRule.AltiumPowerPortLocalNamingRule = void 0;
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	const AltiumNetLocalNamingRule_1 = requireAltiumNetLocalNamingRule();
	class AltiumPowerPortLocalNamingRule$1 extends AltiumNetLocalNamingRule_1.AltiumNetLocalNamingRule {
	    apply(hierarchy) {
	        this.recombobulatePowerPortsWithPorts(hierarchy);
	    }
	    recombobulatePowerPortsWithPorts(hierarchy) {
	        if (hierarchy.instance.isMultiChannel || hierarchy.instance.totalSheetInstances > 1) {
	            this.renamePowerPortsConnectedToPorts(hierarchy);
	        }
	        for (const child of hierarchy.children) {
	            this.recombobulatePowerPortsWithPorts(child);
	        }
	    }
	    renamePowerPortsConnectedToPorts(hierarchy) {
	        const powerPorts = new Map();
	        for (const obj of hierarchy.combobulator.defaultDimension.iterateLabelObjects('power-port')) {
	            const label = obj.label;
	            if (label?.labelType === 'power-port') {
	                const powerPortName = label.formattedLabel ?? label.originalLabel;
	                const ports = powerPorts.get(powerPortName) ?? [];
	                ports.push(label);
	                powerPorts.set(powerPortName, ports);
	            }
	        }
	        for (const powerPortObjs of powerPorts.values()) {
	            const portConnectedObjects = new Map();
	            for (const powerPort of powerPortObjs) {
	                const connectedObjs = Array.from(hierarchy.combobulator.defaultDimension.getObjectsConnectedToPosition(powerPort.position));
	                const connectedPortObjects = connectedObjs.filter((o) => o.label?.labelType === 'port');
	                const usePortNetObject = connectedPortObjects[0];
	                if (!usePortNetObject?.label) {
	                    continue;
	                }
	                portConnectedObjects.set(powerPort, usePortNetObject.label);
	            }
	            if (portConnectedObjects.size === 0) {
	                continue;
	            }
	            for (const powerPort of powerPortObjs) {
	                const useName = this.namingStrategy.formatChannelLocalPowerPortName(powerPort, hierarchy.combobulator.instance);
	                powerPort.formattedLabel = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(powerPort.labelType, useName);
	            }
	        }
	    }
	}
	AltiumPowerPortLocalNamingRule.AltiumPowerPortLocalNamingRule = AltiumPowerPortLocalNamingRule$1;
	
	return AltiumPowerPortLocalNamingRule;
}

var AltiumHarnessLocalNamingRule = {};

var hasRequiredAltiumHarnessLocalNamingRule;

function requireAltiumHarnessLocalNamingRule () {
	if (hasRequiredAltiumHarnessLocalNamingRule) return AltiumHarnessLocalNamingRule;
	hasRequiredAltiumHarnessLocalNamingRule = 1;
	Object.defineProperty(AltiumHarnessLocalNamingRule, "__esModule", { value: true });
	AltiumHarnessLocalNamingRule.AltiumHarnessLocalNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumLabelTypes_1 = requireAltiumLabelTypes();
	const AltiumNetLocalNamingRule_1 = requireAltiumNetLocalNamingRule();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	const AltiumBusRange_1 = requireAltiumBusRange();
	var HarnessHierarchyIdentityKind;
	(function (HarnessHierarchyIdentityKind) {
	    HarnessHierarchyIdentityKind["Port"] = "port";
	    HarnessHierarchyIdentityKind["SheetEntry"] = "sheet-entry";
	})(HarnessHierarchyIdentityKind || (HarnessHierarchyIdentityKind = {}));
	class AltiumHarnessLocalNamingRule$1 extends AltiumNetLocalNamingRule_1.AltiumNetLocalNamingRule {
	    constructor() {
	        super(...arguments);
	        this.sheetEntryCarrierIndexes = new WeakMap();
	    }
	    apply(hierarchy) {
	        this.sheetEntryCarrierIndexes = new WeakMap();
	        const harnessHierarchy = hierarchy;
	        this.expandRecursive(harnessHierarchy);
	        this.cleanupRecursive(harnessHierarchy);
	    }
	    expandRecursive(hierarchy) {
	        this.expandHarnessIslands(hierarchy);
	        for (const child of hierarchy.children) {
	            this.expandRecursive(child);
	        }
	    }
	    cleanupRecursive(hierarchy) {
	        this.removeCarrierObjects(hierarchy.combobulator);
	        for (const child of hierarchy.children) {
	            this.cleanupRecursive(child);
	        }
	    }
	    expandHarnessIslands(hierarchy) {
	        if (!hierarchy.combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return;
	        }
	        const harnessDimension = hierarchy.combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        const processed = new Set();
	        const islands = [];
	        for (const object of Array.from(harnessDimension.iterateAllObjects())) {
	            if (processed.has(object) || !this.isCarrierObject(object)) {
	                continue;
	            }
	            const startPosition = this.getObjectPositionInDimension(object, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	            if (!startPosition) {
	                continue;
	            }
	            const island = Array.from(harnessDimension.getObjectsConnectedToPosition(startPosition)).filter((obj) => this.isCarrierObject(obj));
	            for (const islandObject of island) {
	                processed.add(islandObject);
	            }
	            islands.push(island);
	        }
	        const islandIndexByLabelFold = new Map();
	        const mergeParents = islands.map((_, index) => index);
	        const findRoot = (index) => {
	            let root = index;
	            while (mergeParents[root] !== root) {
	                root = mergeParents[root];
	            }
	            while (mergeParents[index] !== root) {
	                const next = mergeParents[index];
	                mergeParents[index] = root;
	                index = next;
	            }
	            return root;
	        };
	        for (let i = 0; i < islands.length; i++) {
	            for (const name of this.collectCarrierBaseNamesForSources(islands[i], ['carrier-net-label'])) {
	                const fold = name.toLowerCase();
	                const existing = islandIndexByLabelFold.get(fold);
	                if (existing === undefined) {
	                    islandIndexByLabelFold.set(fold, i);
	                    continue;
	                }
	                mergeParents[findRoot(i)] = findRoot(existing);
	            }
	        }
	        const islandIdentities = islands.map((island) => this.collectTypedHierarchyIdentities(hierarchy, island));
	        const islandIsPureLocalBreakout = islands.map((island, index) => this.islandHasLocalBreakout(island) &&
	            !islandIdentities[index].some((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry));
	        const islandIndexesByRoot = islands.map(() => []);
	        for (let i = 0; i < islands.length; i++) {
	            islandIndexesByRoot[findRoot(i)].push(i);
	        }
	        for (let i = 0; i < islands.length; i++) {
	            for (let j = i + 1; j < islands.length; j++) {
	                const rootI = findRoot(i);
	                const rootJ = findRoot(j);
	                if (rootI === rootJ) {
	                    continue;
	                }
	                const bothPureLocal = islandIsPureLocalBreakout[i] && islandIsPureLocalBreakout[j];
	                if (!this.identitiesShareCompatibleMatch(islandIdentities[i], islandIdentities[j], bothPureLocal) ||
	                    this.pureLocalBreakoutIsRelayAmbiguous(islandIdentities[i], islandIsPureLocalBreakout[i], islandIdentities[j], islandIsPureLocalBreakout[j])) {
	                    continue;
	                }
	                if (this.mergeViolatesIslandGuards(islandIdentities, islandIsPureLocalBreakout, islandIndexesByRoot[rootI], islandIndexesByRoot[rootJ])) {
	                    continue;
	                }
	                mergeParents[rootJ] = rootI;
	                islandIndexesByRoot[rootI] = islandIndexesByRoot[rootI].concat(islandIndexesByRoot[rootJ]);
	                islandIndexesByRoot[rootJ] = [];
	            }
	        }
	        const mergedIslands = new Map();
	        for (let i = 0; i < islands.length; i++) {
	            const root = findRoot(i);
	            mergedIslands.set(root, (mergedIslands.get(root) ?? []).concat(islands[i]));
	        }
	        islands.length = 0;
	        islands.push(...mergedIslands.values());
	        islands.sort((a, b) => {
	            const aHasBase = !!this.collectCarrierBaseName(hierarchy.combobulator, a);
	            const bHasBase = !!this.collectCarrierBaseName(hierarchy.combobulator, b);
	            if (aHasBase === bHasBase) {
	                return 0;
	            }
	            return aHasBase ? -1 : 1;
	        });
	        const compatibleMembersByBaseName = this.collectCompatibleMembersByBaseName(hierarchy, islands);
	        for (const island of islands) {
	            this.expandHarnessIsland(hierarchy, island, compatibleMembersByBaseName);
	        }
	    }
	    collectCompatibleMembersByBaseName(hierarchy, islands) {
	        const compatibleMembersByBaseName = new Map();
	        const carrierPositionKey = this.createDimensionPositionKeyer(hierarchy.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        for (const island of islands) {
	            const baseName = this.collectCarrierBaseName(hierarchy.combobulator, island);
	            if (!baseName) {
	                continue;
	            }
	            const targets = this.collectTargets(hierarchy, island);
	            if (targets.length === 0) {
	                continue;
	            }
	            const targetMembers = this.resolveTargetMembers(island, targets, carrierPositionKey);
	            let compatibleMembers = compatibleMembersByBaseName.get(baseName.name);
	            if (!compatibleMembers) {
	                compatibleMembers = new Set();
	                compatibleMembersByBaseName.set(baseName.name, compatibleMembers);
	            }
	            for (const members of targetMembers.values()) {
	                for (const member of members) {
	                    compatibleMembers.add(member);
	                }
	            }
	        }
	        return new Map(Array.from(compatibleMembersByBaseName.entries()).map(([baseName, members]) => [
	            baseName,
	            Array.from(members).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b)),
	        ]));
	    }
	    expandHarnessIsland(hierarchy, island, compatibleMembersByBaseName) {
	        const targets = this.collectTargets(hierarchy, island);
	        if (targets.length === 0) {
	            return;
	        }
	        const carrierPositionKey = this.createDimensionPositionKeyer(hierarchy.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        const baseName = this.collectCarrierBaseName(hierarchy.combobulator, island);
	        const targetMembers = this.resolveTargetMembers(island, targets, carrierPositionKey, baseName ? compatibleMembersByBaseName.get(baseName.name) : undefined);
	        const childMemberNames = this.collectAliasedChildMemberNames(hierarchy, targets);
	        const islandAnchor = island
	            .map((object) => object.endpoints[0]?.position)
	            .find((position) => position !== undefined) ?? [0, 0];
	        const islandKey = `${islandAnchor[0]}:${islandAnchor[1]}`;
	        const memberNames = new Map();
	        if (baseName) {
	            for (const members of targetMembers.values()) {
	                for (const member of members) {
	                    memberNames.set(member, childMemberNames.get(member) ?? `${baseName.name}.${member}`);
	                }
	            }
	        }
	        else {
	            for (const target of targets) {
	                if (target.source !== 'connector-entry') {
	                    continue;
	                }
	                const members = targetMembers.get(target) ?? [];
	                const existingName = this.findRegularNetLabelAtPosition(hierarchy.combobulator, target.position, target.dimension);
	                if (!existingName) {
	                    continue;
	                }
	                for (const member of members) {
	                    memberNames.set(member, existingName);
	                }
	            }
	        }
	        for (const target of targets) {
	            if (target.source !== 'connector-entry') {
	                continue;
	            }
	            const explicitName = this.findRegularNetLabelAtPosition(hierarchy.combobulator, target.position, target.dimension);
	            if (!explicitName) {
	                continue;
	            }
	            for (const member of targetMembers.get(target) ?? []) {
	                if (baseName?.source === 'carrier-net-label' && explicitName.toLowerCase() === member.toLowerCase()) {
	                    continue;
	                }
	                if (this.isCompatibleChildMemberName(explicitName, member)) {
	                    memberNames.set(member, explicitName);
	                }
	            }
	        }
	        if (memberNames.size === 0) {
	            return;
	        }
	        const regularConnectorTargetsByMember = new Map();
	        const connectorCountByMember = new Map();
	        for (const target of targets) {
	            if (target.source !== 'connector-entry') {
	                continue;
	            }
	            for (const member of targetMembers.get(target) ?? []) {
	                connectorCountByMember.set(member, (connectorCountByMember.get(member) ?? 0) + 1);
	                const hasRegularConnectivity = this.hasRegularConnectivityAtPosition(hierarchy.combobulator, target.position, target.dimension);
	                if (hasRegularConnectivity) {
	                    const connectorTargets = regularConnectorTargetsByMember.get(member) ?? [];
	                    connectorTargets.push(target);
	                    regularConnectorTargetsByMember.set(member, connectorTargets);
	                }
	            }
	        }
	        const added = new Set();
	        for (const [member] of memberNames) {
	            const regularConnectorTargets = regularConnectorTargetsByMember.get(member) ?? [];
	            const connectorCount = connectorCountByMember.get(member) ?? 0;
	            if (connectorCount > 0 && regularConnectorTargets.length === connectorCount) {
	                continue;
	            }
	            const memberTargets = targets.filter((target) => target.source !== 'connector-entry' && (targetMembers.get(target) ?? []).includes(member));
	            const [firstTarget, ...remainingTargets] = memberTargets;
	            if (!firstTarget) {
	                continue;
	            }
	            for (const target of remainingTargets) {
	                this.addSyntheticMemberDimensionBridge(hierarchy.combobulator, firstTarget.position, target.position, member, islandKey, added);
	            }
	        }
	        for (const target of targets) {
	            for (const member of targetMembers.get(target) ?? []) {
	                const name = memberNames.get(member);
	                if (!name) {
	                    continue;
	                }
	                const aliasedFromRegularLabel = name !== `${baseName?.name}.${member}`;
	                if (target.source === 'connector-entry') {
	                    if ((regularConnectorTargetsByMember.get(member)?.length ?? 0) === 0) {
	                        continue;
	                    }
	                    if (baseName?.source === 'hierarchy-label') {
	                        continue;
	                    }
	                    this.addSyntheticNetLabel(hierarchy.combobulator, target.position, target.dimension, name, member, added, aliasedFromRegularLabel, baseName?.source === 'carrier-net-label');
	                    continue;
	                }
	                const regularConnectorTargets = regularConnectorTargetsByMember.get(member) ?? [];
	                const connectorHasRegularConnectivity = regularConnectorTargets.length > 0;
	                if (connectorHasRegularConnectivity) {
	                    for (const connectorTarget of regularConnectorTargets) {
	                        this.addSyntheticMemberBridge(hierarchy.combobulator, target.position, connectorTarget.position, connectorTarget.dimension, member, islandKey, added);
	                    }
	                }
	                const usesAliasedChildIdentity = childMemberNames.get(member) === name && name !== `${baseName?.name}.${member}`;
	                if (baseName?.source !== 'hierarchy-label' || usesAliasedChildIdentity) {
	                    this.addSyntheticMemberNetLabel(hierarchy.combobulator, target.position, name, member, islandKey, added, usesAliasedChildIdentity, aliasedFromRegularLabel, baseName?.source === 'carrier-net-label');
	                }
	                if (target.source === 'port' && target.label?.strategyMetadata?.['port-metadata']) {
	                    this.addSyntheticPortLabel(hierarchy.combobulator, target.position, name, member, islandKey, target.label, added);
	                    continue;
	                }
	                if (target.source === 'sheet-entry' && target.label?.strategyMetadata?.['sheet-entry-metadata']) {
	                    this.addSyntheticSheetEntryLabel(hierarchy.combobulator, target.position, name, member, islandKey, target.label, added);
	                }
	            }
	        }
	    }
	    collectAliasedChildMemberNames(hierarchy, targets) {
	        const sheetEntryTargets = targets.filter((target) => target.source === 'sheet-entry' && !!target.label);
	        if (sheetEntryTargets.length < 2) {
	            return new Map();
	        }
	        const namesByMember = new Map();
	        for (const target of sheetEntryTargets) {
	            const sheetEntryMetadata = target.label.strategyMetadata?.['sheet-entry-metadata'];
	            if (!sheetEntryMetadata) {
	                continue;
	            }
	            for (const child of hierarchy.children) {
	                if (child.instance.sheetSymbol?.uniqueId !== sheetEntryMetadata.sheetSymbolUniqueId) {
	                    continue;
	                }
	                if (child.instance.isMultiChannel || child.instance.totalSheetInstances > 1) {
	                    continue;
	                }
	                const childPort = this.findChildPortCarrierObject(child, sheetEntryMetadata.sheetEntryName);
	                if (!childPort?.label) {
	                    continue;
	                }
	                const childIsland = this.getCarrierIslandForObject(child.combobulator, childPort);
	                const childTargets = this.collectTargets(child, childIsland);
	                const positionKey = this.createDimensionPositionKeyer(child.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	                const membersByTarget = this.resolveTargetMembers(childIsland, childTargets, positionKey);
	                for (const childTarget of childTargets) {
	                    if (childTarget.source !== 'connector-entry') {
	                        continue;
	                    }
	                    const explicitName = this.findRegularNetLabelAtPosition(child.combobulator, childTarget.position, childTarget.dimension);
	                    if (!explicitName) {
	                        continue;
	                    }
	                    for (const member of membersByTarget.get(childTarget) ?? []) {
	                        if (!this.isCompatibleChildMemberName(explicitName, member)) {
	                            continue;
	                        }
	                        if (this.dereferenceLabelName(explicitName) === member) {
	                            continue;
	                        }
	                        if (!namesByMember.has(member)) {
	                            namesByMember.set(member, new Set());
	                        }
	                        namesByMember.get(member).add(explicitName);
	                    }
	                }
	            }
	        }
	        const result = new Map();
	        for (const [member, names] of namesByMember) {
	            if (names.size === 1) {
	                result.set(member, Array.from(names)[0]);
	            }
	        }
	        return result;
	    }
	    isCompatibleChildMemberName(name, member) {
	        const dereferencedName = this.dereferenceLabelName(name);
	        if (dereferencedName === member || dereferencedName.endsWith(`.${member}`)) {
	            return true;
	        }
	        if (!member.endsWith('n')) {
	            return false;
	        }
	        const escapedPrefix = member.slice(0, -1).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	        return new RegExp(`(?:^|\\.)${escapedPrefix}\\d+n$`).test(dereferencedName);
	    }
	    resolveTargetMembers(island, targets, positionKey, additionalCompatibleMembers = []) {
	        const membersByTarget = new Map();
	        const setMembers = (target, members) => {
	            if (!membersByTarget.has(target)) {
	                membersByTarget.set(target, new Set());
	            }
	            for (const member of members) {
	                if (member.trim()) {
	                    membersByTarget.get(target).add(member);
	                }
	            }
	        };
	        for (const target of targets) {
	            if (target.source === 'connector-entry' && target.harnessMemberType) {
	                continue;
	            }
	            setMembers(target, target.members);
	        }
	        const connectorTargetsByPosition = new Map();
	        const targetsByPosition = new Map();
	        const nestedAssignedTargets = new Set();
	        for (const target of targets) {
	            const key = positionKey(target.position);
	            if (!targetsByPosition.has(key)) {
	                targetsByPosition.set(key, []);
	            }
	            targetsByPosition.get(key).push(target);
	            if (target.source !== 'connector-entry') {
	                continue;
	            }
	            if (!connectorTargetsByPosition.has(key)) {
	                connectorTargetsByPosition.set(key, []);
	            }
	            connectorTargetsByPosition.get(key).push(target);
	        }
	        for (const branchTarget of targets) {
	            if (branchTarget.source !== 'connector-entry' || !branchTarget.harnessMemberType) {
	                continue;
	            }
	            this.assignNestedBranchMembers(island, targetsByPosition, branchTarget, branchTarget.members[0], membersByTarget, nestedAssignedTargets, positionKey);
	        }
	        this.assignCompatibleNestedMembers(membersByTarget, additionalCompatibleMembers);
	        return new Map(Array.from(membersByTarget.entries()).map(([target, members]) => [
	            target,
	            Array.from(members).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b)),
	        ]));
	    }
	    assignCompatibleNestedMembers(membersByTarget, additionalCompatibleMembers = []) {
	        const allMembers = Array.from(new Set([
	            ...Array.from(membersByTarget.values()).flatMap((members) => Array.from(members)),
	            ...additionalCompatibleMembers,
	        ]));
	        for (const [target, members] of membersByTarget) {
	            if (target.source === 'connector-entry') {
	                continue;
	            }
	            for (const member of Array.from(members)) {
	                for (const candidate of allMembers) {
	                    if (candidate !== member && candidate.startsWith(`${member}.`)) {
	                        members.add(candidate);
	                    }
	                }
	            }
	        }
	    }
	    assignNestedBranchMembers(island, targetsByPosition, branchTarget, prefix, membersByTarget, nestedAssignedTargets, positionKey) {
	        const reachablePositions = this.collectBranchReachablePositions(island, branchTarget.position, positionKey);
	        for (const reachablePositionKey of reachablePositions) {
	            for (const target of targetsByPosition.get(reachablePositionKey) ?? []) {
	                if (target === branchTarget) {
	                    continue;
	                }
	                if (target.harnessMemberType) {
	                    this.assignNestedBranchMembers(island, targetsByPosition, target, `${prefix}.${target.members[0]}`, membersByTarget, nestedAssignedTargets, positionKey);
	                    continue;
	                }
	                if (!nestedAssignedTargets.has(target)) {
	                    membersByTarget.set(target, new Set());
	                    nestedAssignedTargets.add(target);
	                }
	                for (const member of target.members) {
	                    membersByTarget.get(target).add(`${prefix}.${member}`);
	                }
	            }
	        }
	    }
	    collectBranchReachablePositions(island, startPosition, positionKey) {
	        const edges = this.collectCarrierEdges(island);
	        const edgesByPosition = new Map();
	        for (const edge of edges) {
	            for (const position of [edge.from, edge.to]) {
	                const key = positionKey(position);
	                if (!edgesByPosition.has(key)) {
	                    edgesByPosition.set(key, []);
	                }
	                edgesByPosition.get(key).push(edge);
	            }
	        }
	        const startKey = positionKey(startPosition);
	        const visitedPositions = new Set();
	        const visitedEdges = new Set();
	        const queue = [];
	        for (const edge of edgesByPosition.get(startKey) ?? []) {
	            if (edge.internal) {
	                continue;
	            }
	            const other = this.edgeOtherPosition(edge, startPosition, positionKey);
	            if (other) {
	                visitedEdges.add(this.edgeKey(edge, positionKey));
	                queue.push(other);
	            }
	        }
	        while (queue.length > 0) {
	            const position = queue.shift();
	            const currentPositionKey = positionKey(position);
	            if (visitedPositions.has(currentPositionKey)) {
	                continue;
	            }
	            visitedPositions.add(currentPositionKey);
	            for (const edge of edgesByPosition.get(currentPositionKey) ?? []) {
	                const edgeKey = this.edgeKey(edge, positionKey);
	                if (visitedEdges.has(edgeKey)) {
	                    continue;
	                }
	                visitedEdges.add(edgeKey);
	                const other = this.edgeOtherPosition(edge, position, positionKey);
	                if (other && positionKey(other) !== startKey) {
	                    queue.push(other);
	                }
	            }
	        }
	        return visitedPositions;
	    }
	    collectCarrierEdges(island) {
	        const edges = [];
	        for (const object of island) {
	            if (!object.wire) {
	                continue;
	            }
	            const endpoints = object.endpoints.filter((endpoint) => endpoint.dimension === String(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness));
	            if (endpoints.length < 2) {
	                continue;
	            }
	            edges.push({
	                from: endpoints[0].position,
	                to: endpoints[1].position,
	                internal: object.wire.strategyMetadata?.harness?.harnessSource === 'connector-internal',
	            });
	        }
	        return edges;
	    }
	    collectTargets(hierarchy, island) {
	        const targets = [];
	        for (const object of island) {
	            const metadata = object.label?.strategyMetadata?.harness ?? object.wire?.strategyMetadata?.harness;
	            if (!metadata?.harnessSource) {
	                continue;
	            }
	            const position = this.getObjectPositionInDimension(object, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	            if (!position) {
	                continue;
	            }
	            if (metadata.harnessSource === 'connector-entry' && metadata.harnessMemberName) {
	                if (!this.isConcreteHarnessType(metadata.harnessType)) {
	                    continue;
	                }
	                if (!metadata.isHarnessDefinitionEntry) {
	                    continue;
	                }
	                targets.push({
	                    source: 'connector-entry',
	                    position,
	                    dimension: metadata.harnessMemberDimension,
	                    members: [metadata.harnessMemberName],
	                    harnessMemberType: metadata.harnessMemberType,
	                    label: object.label,
	                });
	                continue;
	            }
	            if (metadata.harnessSource === 'port' && object.label) {
	                let members = this.getExpandedMembersForHarnessType(hierarchy, metadata.harnessType);
	                if (members.length === 0) {
	                    members = this.inferMembersFromCarrierIsland(hierarchy, island, object.label);
	                }
	                if (members.length > 0) {
	                    targets.push({ source: 'port', position, members, label: object.label });
	                }
	                continue;
	            }
	            if (metadata.harnessSource === 'sheet-entry' && object.label) {
	                let members = this.getMembersForSheetEntry(hierarchy, object.label);
	                if (members.length === 0) {
	                    members = this.inferMembersFromCarrierIsland(hierarchy, island, object.label);
	                }
	                if (members.length > 0) {
	                    targets.push({ source: 'sheet-entry', position, members, label: object.label });
	                }
	            }
	        }
	        return targets;
	    }
	    getMembersForHarnessType(hierarchy, harnessType) {
	        if (!this.isConcreteHarnessType(harnessType)) {
	            return [];
	        }
	        return hierarchy.instance.hierarchyEntry.schematic.getHarnessDefinitionMembers(harnessType);
	    }
	    getHarnessDefinitionEntries(hierarchy, harnessType) {
	        if (!this.isConcreteHarnessType(harnessType)) {
	            return [];
	        }
	        const schematic = hierarchy.instance.hierarchyEntry.schematic;
	        const entries = schematic.getHarnessDefinitionEntries?.(harnessType);
	        if (!entries?.length) {
	            return (schematic.getHarnessDefinitionMembers?.(harnessType) ?? []).map((name) => ({ name }));
	        }
	        const nestedTypesByMember = this.getConnectorEntryNestedTypes(hierarchy.combobulator, harnessType);
	        return entries.map((entry) => this.resolveDefinitionEntryHarnessType(entry, nestedTypesByMember));
	    }
	    resolveDefinitionEntryHarnessType(entry, nestedTypesByMember) {
	        if (nestedTypesByMember.has(entry.name)) {
	            return { name: entry.name, harnessType: nestedTypesByMember.get(entry.name) };
	        }
	        const memberNames = AltiumBusRange_1.AltiumBusRange.parse(entry.name)?.members ?? [entry.name];
	        const observedTypes = memberNames
	            .filter((memberName) => nestedTypesByMember.has(memberName))
	            .map((memberName) => nestedTypesByMember.get(memberName));
	        if (observedTypes.length === 0) {
	            return entry;
	        }
	        const resolvedType = observedTypes[0];
	        const foldedResolvedType = resolvedType?.toLowerCase();
	        const allMembersAgree = observedTypes.length === memberNames.length &&
	            observedTypes.every((observedType) => observedType?.toLowerCase() === foldedResolvedType);
	        return allMembersAgree ? { name: entry.name, harnessType: resolvedType } : { name: entry.name };
	    }
	    getConnectorEntryNestedTypes(combobulator, harnessType) {
	        const nestedTypesByMember = new Map();
	        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return nestedTypesByMember;
	        }
	        const foldedHarnessType = harnessType.toLowerCase();
	        for (const object of combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness).iterateAllObjects()) {
	            const metadata = object.label?.strategyMetadata?.harness;
	            if (metadata?.harnessSource !== 'connector-entry' ||
	                !metadata.isHarnessDefinitionEntry ||
	                metadata.harnessType?.toLowerCase() !== foldedHarnessType ||
	                !metadata.harnessMemberName) {
	                continue;
	            }
	            const nestedType = nestedTypesByMember.get(metadata.harnessMemberName) ?? metadata.harnessMemberType;
	            nestedTypesByMember.set(metadata.harnessMemberName, nestedType);
	        }
	        return nestedTypesByMember;
	    }
	    getExpandedMembersForHarnessType(hierarchy, harnessType, visited = new Set()) {
	        if (!this.isConcreteHarnessType(harnessType) || visited.has(harnessType.toLowerCase())) {
	            return [];
	        }
	        visited.add(harnessType.toLowerCase());
	        const members = [];
	        for (const entry of this.getHarnessDefinitionEntries(hierarchy, harnessType)) {
	            if (entry.harnessType) {
	                const childMembers = this.getExpandedMembersForHarnessType(hierarchy, entry.harnessType, new Set(visited));
	                if (childMembers.length > 0) {
	                    members.push(...childMembers.map((member) => `${entry.name}.${member}`));
	                    continue;
	                }
	            }
	            members.push(...(AltiumBusRange_1.AltiumBusRange.parse(entry.name)?.members ?? [entry.name]));
	        }
	        return members;
	    }
	    isConcreteHarnessType(harnessType) {
	        return !!harnessType?.trim() && harnessType.trim() !== '*';
	    }
	    getMembersForSheetEntry(hierarchy, label) {
	        const harnessType = label.strategyMetadata?.harness?.harnessType;
	        const localMembers = this.getExpandedMembersForHarnessType(hierarchy, harnessType);
	        const sheetEntryMetadata = label.strategyMetadata?.['sheet-entry-metadata'];
	        if (!sheetEntryMetadata) {
	            return localMembers;
	        }
	        const childMembers = [];
	        for (const child of hierarchy.children) {
	            if (child.instance.sheetSymbol?.uniqueId !== sheetEntryMetadata.sheetSymbolUniqueId) {
	                continue;
	            }
	            const childHarnessType = this.findChildPortHarnessType(child, sheetEntryMetadata.sheetEntryName);
	            const expandedChildMembers = this.getExpandedMembersForHarnessType(child, childHarnessType);
	            const inferredChildMembers = this.inferMembersForChildPort(child, sheetEntryMetadata.sheetEntryName);
	            if (expandedChildMembers.length > 0) {
	                childMembers.push(...this.expandMembersWithCompatibleMembers(expandedChildMembers, inferredChildMembers));
	                continue;
	            }
	            if (inferredChildMembers.length > 0) {
	                childMembers.push(...inferredChildMembers);
	            }
	        }
	        if (localMembers.length === 0) {
	            return Array.from(new Set(childMembers)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	        }
	        return Array.from(new Set([...localMembers, ...childMembers])).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	    }
	    expandMembersWithCompatibleMembers(members, compatibleMembers) {
	        if (compatibleMembers.length === 0) {
	            return members;
	        }
	        const expandedMembers = new Set(members);
	        for (const member of members) {
	            for (const compatibleMember of compatibleMembers) {
	                if (compatibleMember === member || compatibleMember.startsWith(`${member}.`)) {
	                    expandedMembers.add(compatibleMember);
	                }
	            }
	        }
	        return Array.from(expandedMembers).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	    }
	    inferMembersForChildPort(hierarchy, portName) {
	        const portObject = this.findChildPortCarrierObject(hierarchy, portName);
	        if (!portObject?.label) {
	            return [];
	        }
	        const island = this.getCarrierIslandForObject(hierarchy.combobulator, portObject);
	        if (island.length === 0) {
	            return [];
	        }
	        const targets = this.collectTargets(hierarchy, island);
	        const portTargets = targets.filter((target) => target.source === 'port' && target.label === portObject.label);
	        if (portTargets.length > 0) {
	            const positionKey = this.createDimensionPositionKeyer(hierarchy.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	            const targetMembers = this.resolveTargetMembers(island, targets, positionKey);
	            const resolvedMembers = portTargets.flatMap((target) => targetMembers.get(target) ?? []);
	            if (resolvedMembers.length > 0) {
	                return Array.from(new Set(resolvedMembers)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	            }
	        }
	        return this.inferMembersFromCarrierIsland(hierarchy, island, portObject.label);
	    }
	    getCarrierIslandForObject(combobulator, object) {
	        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return [];
	        }
	        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        const island = new Set();
	        for (const endpoint of object.endpoints) {
	            if (endpoint.dimension !== String(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	                continue;
	            }
	            for (const connectedObject of harnessDimension.getObjectsConnectedToPosition(endpoint.position)) {
	                if (this.isCarrierObject(connectedObject)) {
	                    island.add(connectedObject);
	                }
	            }
	        }
	        return Array.from(island);
	    }
	    inferMembersFromCarrierIsland(hierarchy, island, excludeLabel) {
	        const members = new Set();
	        const addMembers = (candidateMembers) => {
	            for (const member of candidateMembers) {
	                if (member.trim()) {
	                    members.add(member);
	                }
	            }
	        };
	        for (const object of island) {
	            const label = object.label;
	            const metadata = label?.strategyMetadata?.harness;
	            if (!label || label === excludeLabel || !metadata?.harnessSource) {
	                continue;
	            }
	            if (metadata.harnessSource === 'connector-entry' && metadata.harnessMemberName && !metadata.harnessMemberType) {
	                if (this.isConcreteHarnessType(metadata.harnessType) && metadata.isHarnessDefinitionEntry) {
	                    members.add(metadata.harnessMemberName);
	                }
	                continue;
	            }
	            if (metadata.harnessSource === 'port') {
	                addMembers(this.getExpandedMembersForHarnessType(hierarchy, metadata.harnessType));
	                continue;
	            }
	            if (metadata.harnessSource === 'sheet-entry') {
	                addMembers(this.getMembersForSheetEntry(hierarchy, label));
	            }
	        }
	        return Array.from(members).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	    }
	    findChildPortHarnessType(hierarchy, portName) {
	        const object = this.findChildPortCarrierObject(hierarchy, portName);
	        return object?.label?.strategyMetadata?.harness?.harnessType;
	    }
	    findChildPortCarrierObject(hierarchy, portName) {
	        if (!hierarchy.combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return undefined;
	        }
	        const harnessDimension = hierarchy.combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        for (const object of harnessDimension.iterateLabelObjects('port')) {
	            if (object.label?.strategyMetadata?.['port-metadata']?.portName !== portName) {
	                continue;
	            }
	            return object;
	        }
	        return undefined;
	    }
	    collectCarrierBaseName(combobulator, island) {
	        const carrierNetLabelNames = this.collectCarrierBaseNamesForSources(island, ['carrier-net-label']);
	        if (carrierNetLabelNames.length > 0) {
	            return { name: carrierNetLabelNames[0], source: 'carrier-net-label' };
	        }
	        const regularNetLabelNames = this.collectRegularNetLabelBaseNames(combobulator, island);
	        if (regularNetLabelNames.length > 0) {
	            return { name: regularNetLabelNames[0], source: 'regular-net-label' };
	        }
	        const portAndSheetEntryNames = this.collectCarrierBaseNamesForSources(island, ['port', 'sheet-entry']);
	        if (portAndSheetEntryNames.length > 0) {
	            return {
	                name: this.qualifyHierarchyLabelBaseName(combobulator, island, portAndSheetEntryNames[0]),
	                source: 'hierarchy-label',
	            };
	        }
	        return undefined;
	    }
	    qualifyHierarchyLabelBaseName(combobulator, island, name) {
	        const foldedName = name.toLowerCase();
	        const ownHarnessType = this.selectOwnSheetEntryHarnessType(island, foldedName);
	        if (!ownHarnessType || !combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return name;
	        }
	        const islandObjects = new Set(island);
	        const foldedOwnHarnessType = ownHarnessType.toLowerCase();
	        const collidesWithDifferentType = this.getSheetEntryCarrierIndex(combobulator).some((entry) => !islandObjects.has(entry.object) &&
	            entry.foldedName === foldedName &&
	            entry.foldedHarnessType !== foldedOwnHarnessType);
	        return collidesWithDifferentType ? `${ownHarnessType}#${name}` : name;
	    }
	    selectOwnSheetEntryHarnessType(island, foldedName) {
	        let selected;
	        for (const object of island) {
	            const label = object.label;
	            const metadata = label?.strategyMetadata?.harness;
	            if (metadata?.harnessSource !== 'sheet-entry' ||
	                this.dereferenceLabelName(label?.originalLabel ?? '').toLowerCase() !== foldedName) {
	                continue;
	            }
	            const harnessType = metadata.harnessType;
	            if (!this.isConcreteHarnessType(harnessType)) {
	                continue;
	            }
	            if (selected === undefined || sigma_engine_shared_1.Utils.naturalStringSort(harnessType.toLowerCase(), selected.toLowerCase()) < 0) {
	                selected = harnessType;
	            }
	        }
	        return selected;
	    }
	    getSheetEntryCarrierIndex(combobulator) {
	        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        const cached = this.sheetEntryCarrierIndexes.get(harnessDimension);
	        if (cached) {
	            return cached;
	        }
	        const index = [];
	        for (const object of harnessDimension.iterateAllObjects()) {
	            const metadata = object.label?.strategyMetadata?.harness;
	            if (metadata?.harnessSource !== 'sheet-entry' || !this.isConcreteHarnessType(metadata.harnessType)) {
	                continue;
	            }
	            index.push({
	                object,
	                foldedName: this.dereferenceLabelName(object.label?.originalLabel ?? '').toLowerCase(),
	                foldedHarnessType: (metadata.harnessType ?? '').toLowerCase(),
	            });
	        }
	        this.sheetEntryCarrierIndexes.set(harnessDimension, index);
	        return index;
	    }
	    collectCarrierBaseNamesForSources(island, sources) {
	        const sourceSet = new Set(sources);
	        const names = island
	            .filter((object) => {
	            const source = object.label?.strategyMetadata?.harness?.harnessSource;
	            return source ? sourceSet.has(source) : false;
	        })
	            .map((object) => object.label)
	            .filter((label) => !!label)
	            .map((label) => label.originalLabel)
	            .filter((name) => !!name?.trim())
	            .map((name) => this.dereferenceLabelName(name));
	        return Array.from(new Set(names)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	    }
	    collectTypedHierarchyIdentities(hierarchy, island) {
	        const identities = [];
	        for (const object of island) {
	            const label = object.label;
	            const metadata = label?.strategyMetadata?.harness;
	            if (!label || (metadata?.harnessSource !== 'port' && metadata?.harnessSource !== 'sheet-entry')) {
	                continue;
	            }
	            const harnessType = metadata.harnessType;
	            if (!this.isConcreteHarnessType(harnessType)) {
	                continue;
	            }
	            const members = this.getExpandedMembersForHarnessType(hierarchy, harnessType);
	            if (members.length === 0) {
	                continue;
	            }
	            const name = this.dereferenceLabelName(label.originalLabel).toLowerCase();
	            if (!name.trim()) {
	                continue;
	            }
	            identities.push({
	                kind: metadata.harnessSource === 'port' ?
	                    HarnessHierarchyIdentityKind.Port
	                    : HarnessHierarchyIdentityKind.SheetEntry,
	                name,
	                harnessType: harnessType.toLowerCase(),
	                members: Array.from(new Set(members)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b)),
	            });
	        }
	        return identities;
	    }
	    islandHasLocalBreakout(island) {
	        return island.some((object) => {
	            const metadata = object.label?.strategyMetadata?.harness ?? object.wire?.strategyMetadata?.harness;
	            return (metadata?.harnessSource === 'connector-entry' &&
	                !!metadata.harnessMemberName &&
	                !!metadata.isHarnessDefinitionEntry &&
	                this.isConcreteHarnessType(metadata.harnessType));
	        });
	    }
	    areHierarchyIdentitiesCompatible(a, b) {
	        if (a.name !== b.name) {
	            return false;
	        }
	        const sameType = a.harnessType === b.harnessType;
	        const sameMembers = a.members.length === b.members.length && a.members.every((member, index) => member === b.members[index]);
	        return sameType || sameMembers;
	    }
	    identitiesShareCompatibleMatch(identitiesA, identitiesB, allowSingleMember) {
	        return identitiesA.some((a) => identitiesB.some((b) => {
	            if (a.kind === HarnessHierarchyIdentityKind.SheetEntry && b.kind === HarnessHierarchyIdentityKind.SheetEntry) {
	                return false;
	            }
	            if (!allowSingleMember && (a.members.length < 2 || b.members.length < 2)) {
	                return false;
	            }
	            return this.areHierarchyIdentitiesCompatible(a, b);
	        }));
	    }
	    mergeViolatesIslandGuards(islandIdentities, islandIsPureLocalBreakout, groupA, groupB) {
	        return groupA.some((a) => groupB.some((b) => this.identitiesRepeatASheetEntry(islandIdentities[a], islandIdentities[b]) ||
	            this.pureLocalBreakoutIsRelayAmbiguous(islandIdentities[a], islandIsPureLocalBreakout[a], islandIdentities[b], islandIsPureLocalBreakout[b])));
	    }
	    identitiesRepeatASheetEntry(identitiesA, identitiesB) {
	        return sigma_engine_shared_1.Utils.ChainableIterator.from(identitiesA)
	            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry)
	            .some((a) => sigma_engine_shared_1.Utils.ChainableIterator.from(identitiesB)
	            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry)
	            .some((b) => this.areHierarchyIdentitiesCompatible(a, b)));
	    }
	    pureLocalBreakoutIsRelayAmbiguous(identitiesA, isPureLocalA, identitiesB, isPureLocalB) {
	        if (isPureLocalA === isPureLocalB) {
	            return false;
	        }
	        const [local, other] = isPureLocalA ? [identitiesA, identitiesB] : [identitiesB, identitiesA];
	        if (!sigma_engine_shared_1.Utils.ChainableIterator.from(other)
	            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry)
	            .some(() => true)) {
	            return false;
	        }
	        return sigma_engine_shared_1.Utils.ChainableIterator.from(local)
	            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.Port)
	            .some((a) => sigma_engine_shared_1.Utils.ChainableIterator.from(other)
	            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.Port)
	            .some((b) => a.name === b.name && a.harnessType === b.harnessType));
	    }
	    collectRegularNetLabelBaseNames(combobulator, island) {
	        return this.collectRegularLabelBaseNames(combobulator, island, ['net-label']);
	    }
	    collectRegularLabelBaseNames(combobulator, island, labelTypes) {
	        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
	            return [];
	        }
	        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
	        const islandObjects = new Set(island);
	        const names = [];
	        const collectLabel = (label) => {
	            if (!label || !labelTypes.includes(label.labelType)) {
	                return;
	            }
	            if (harnessDimension
	                .getObjectsAtPosition(label.position)
	                .some((object) => islandObjects.has(object) && object.label?.strategyMetadata?.harness?.harnessSource === 'connector-entry')) {
	                return;
	            }
	            if (label.strategyMetadata?.harness?.isCarrier ||
	                label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-')) {
	                return;
	            }
	            const name = this.dereferenceLabelName(label.originalLabel);
	            if (name.trim()) {
	                names.push(name);
	            }
	        };
	        for (const labelType of labelTypes) {
	            for (const labelObject of combobulator.defaultDimension.iterateLabelObjects(labelType)) {
	                const label = labelObject.label;
	                if (!label) {
	                    continue;
	                }
	                const touchesIsland = harnessDimension
	                    .getObjectsAtPosition(label.position)
	                    .some((object) => islandObjects.has(object) && this.isCarrierBaseGeometryObject(object));
	                if (touchesIsland) {
	                    collectLabel(label);
	                }
	            }
	        }
	        for (const position of this.collectCarrierBaseTraversalPositions(island)) {
	            for (const object of combobulator.defaultDimension.getObjectsConnectedToPosition(position)) {
	                collectLabel(object.label);
	            }
	        }
	        return Array.from(new Set(names)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
	    }
	    collectCarrierBaseTraversalPositions(island) {
	        const positions = [];
	        const seen = new Set();
	        const addPosition = (position) => {
	            const key = this.positionKey(position);
	            if (seen.has(key)) {
	                return;
	            }
	            seen.add(key);
	            positions.push(position);
	        };
	        for (const object of island) {
	            if (object.label && this.isCarrierBaseLabelObject(object)) {
	                addPosition(object.label.position);
	            }
	        }
	        return positions;
	    }
	    isCarrierBaseGeometryObject(object) {
	        if (this.isCarrierBaseLabelObject(object)) {
	            return true;
	        }
	        return object.wire?.strategyMetadata?.harness?.harnessSource === 'signal-harness';
	    }
	    isCarrierBaseLabelObject(object) {
	        const source = object.label?.strategyMetadata?.harness?.harnessSource;
	        return source === 'carrier-net-label' || source === 'port' || source === 'sheet-entry';
	    }
	    findRegularNetLabelAtPosition(combobulator, position, dimension) {
	        const regularDimension = this.getRegularDimension(combobulator, dimension);
	        const connectedLabels = Array.from(regularDimension.getObjectsConnectedToPosition(position))
	            .map((object) => object.label)
	            .filter((label) => label?.labelType === 'net-label')
	            .filter((label) => !label.strategyMetadata?.harness?.isCarrier);
	        const syntheticLabels = connectedLabels
	            .filter((label) => label.strategyMetadata?.harness?.harnessSource === 'synthetic-net-label')
	            .map((label) => this.dereferenceLabelName(label.originalLabel))
	            .filter((name) => !!name.trim());
	        if (syntheticLabels.length > 0) {
	            return Array.from(new Set(syntheticLabels)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
	        }
	        const labels = connectedLabels
	            .map((label) => this.dereferenceLabelName(label.originalLabel))
	            .filter((name) => !!name.trim());
	        if (labels.length === 0) {
	            return undefined;
	        }
	        const sharedSyntheticLabels = this.findSyntheticLabelsSharingRegularLabel(combobulator, labels);
	        if (sharedSyntheticLabels.length > 0) {
	            return Array.from(new Set(sharedSyntheticLabels)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
	        }
	        return Array.from(new Set(labels)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
	    }
	    findSyntheticLabelsSharingRegularLabel(combobulator, regularNames) {
	        const regularNameSet = new Set(regularNames.map((name) => this.dereferenceLabelName(name)));
	        const syntheticNames = [];
	        for (const labelObject of combobulator.defaultDimension.iterateLabelObjects('net-label')) {
	            const label = labelObject.label;
	            if (!label || label.strategyMetadata?.harness?.isCarrier) {
	                continue;
	            }
	            const labelName = this.dereferenceLabelName(label.originalLabel);
	            if (!regularNameSet.has(labelName)) {
	                continue;
	            }
	            for (const connectedObject of combobulator.defaultDimension.getObjectsConnectedToPosition(label.position)) {
	                const connectedLabel = connectedObject.label;
	                if (connectedLabel?.labelType !== 'net-label' ||
	                    connectedLabel.strategyMetadata?.harness?.harnessSource !== 'synthetic-net-label') {
	                    continue;
	                }
	                const syntheticName = this.dereferenceLabelName(connectedLabel.originalLabel);
	                if (syntheticName.trim()) {
	                    syntheticNames.push(syntheticName);
	                }
	            }
	        }
	        return syntheticNames;
	    }
	    hasRegularConnectivityAtPosition(combobulator, position, dimension) {
	        return Array.from(this.getRegularDimension(combobulator, dimension).getObjectsConnectedToPosition(position)).some((object) => !object.label?.strategyMetadata?.harness?.isCarrier);
	    }
	    getRegularDimension(combobulator, dimension) {
	        return dimension && combobulator.hasDimension(dimension) ?
	            combobulator.getDimension(dimension)
	            : combobulator.defaultDimension;
	    }
	    addSyntheticNetLabel(combobulator, position, dimension, name, member, added, aliasedFromRegularLabel = false, bundleNamedByCarrierLabel = false) {
	        const key = `net-label:${position[0]}:${position[1]}:${member}:${name}`;
	        if (added.has(key)) {
	            return;
	        }
	        added.add(key);
	        combobulator.setLabel(this.wireEndpoint(position, dimension), name, 'net-label', {
	            harness: {
	                harnessMemberName: member,
	                preservePhysicalName: false,
	                aliasedFromRegularLabel,
	                bundleNamedByCarrierLabel,
	                harnessSource: 'synthetic-net-label',
	            },
	        });
	    }
	    addSyntheticMemberNetLabel(combobulator, position, name, member, islandKey, added, preservePhysicalName = false, aliasedFromRegularLabel = false, bundleNamedByCarrierLabel = false) {
	        const key = `member-net-label:${position[0]}:${position[1]}:${member}:${name}`;
	        if (added.has(key)) {
	            return;
	        }
	        added.add(key);
	        combobulator.setLabel([this.memberEndpoint(position, member, islandKey)], name, 'net-label', {
	            harness: {
	                harnessMemberName: member,
	                preservePhysicalName,
	                aliasedFromRegularLabel,
	                bundleNamedByCarrierLabel,
	                harnessSource: 'synthetic-net-label',
	            },
	        });
	    }
	    addSyntheticMemberBridge(combobulator, fromPosition, toPosition, toDimension, member, islandKey, added) {
	        const key = `bridge:${fromPosition[0]}:${fromPosition[1]}:${toPosition[0]}:${toPosition[1]}:${member}`;
	        if (added.has(key)) {
	            return;
	        }
	        added.add(key);
	        combobulator.addWire({
	            from: this.memberEndpoint(fromPosition, member, islandKey),
	            to: this.wireEndpoint(toPosition, toDimension)[0],
	            strategyMetadata: {
	                harness: {
	                    isCarrier: false,
	                    harnessMemberName: member,
	                    harnessSource: 'synthetic-member-bridge',
	                },
	            },
	        });
	    }
	    addSyntheticMemberDimensionBridge(combobulator, fromPosition, toPosition, member, islandKey, added) {
	        const key = `member-dimension-bridge:${fromPosition[0]}:${fromPosition[1]}:${toPosition[0]}:${toPosition[1]}:${member}`;
	        if (added.has(key)) {
	            return;
	        }
	        added.add(key);
	        combobulator.addWire({
	            from: this.memberEndpoint(fromPosition, member, islandKey),
	            to: this.memberEndpoint(toPosition, member, islandKey),
	            strategyMetadata: {
	                harness: {
	                    isCarrier: false,
	                    harnessMemberName: member,
	                    harnessSource: 'synthetic-member-bridge',
	                },
	            },
	        });
	    }
	    addSyntheticPortLabel(combobulator, position, name, member, islandKey, sourceLabel, added) {
	        const portMetadata = sourceLabel.strategyMetadata?.['port-metadata'];
	        if (!portMetadata) {
	            return;
	        }
	        const key = `port:${position[0]}:${position[1]}:${member}:${name}`;
	        if (added.has(key)) {
	            return;
	        }
	        added.add(key);
	        combobulator.setLabel([this.memberEndpoint(position, member, islandKey)], name, 'port', {
	            'port-metadata': portMetadata,
	            harness: {
	                ...sourceLabel.strategyMetadata?.harness,
	                isCarrier: false,
	                harnessMemberName: member,
	                harnessSource: 'synthetic-port',
	            },
	        });
	    }
	    addSyntheticSheetEntryLabel(combobulator, position, name, member, islandKey, sourceLabel, added) {
	        const sheetEntryMetadata = sourceLabel.strategyMetadata?.['sheet-entry-metadata'];
	        if (!sheetEntryMetadata) {
	            return;
	        }
	        const key = `sheet-entry:${position[0]}:${position[1]}:${member}:${name}`;
	        if (added.has(key)) {
	            return;
	        }
	        added.add(key);
	        combobulator.setLabel([this.memberEndpoint(position, member, islandKey)], name, 'sheet-entry', {
	            'sheet-entry-metadata': sheetEntryMetadata,
	            harness: {
	                ...sourceLabel.strategyMetadata?.harness,
	                isCarrier: false,
	                harnessMemberName: member,
	                harnessSource: 'synthetic-sheet-entry',
	            },
	        }, { canNameNet: false });
	    }
	    wireEndpoint(position, dimension) {
	        return [{ position, dimension: dimension ?? AltiumLabelTypes_1.AltiumCombobulationDimensions.Wire }];
	    }
	    memberEndpoint(position, member, islandKey) {
	        return { position, dimension: `${AltiumLabelTypes_1.AltiumCombobulationDimensions.Wire}:harness-member:${islandKey}:${member}` };
	    }
	    removeCarrierObjects(combobulator) {
	        const carrierObjects = Array.from(combobulator.iterateAllObjects()).filter((object) => this.isCarrierObject(object));
	        for (const object of carrierObjects) {
	            combobulator.removeObject(object);
	        }
	    }
	    isCarrierObject(object) {
	        if (object.label?.strategyMetadata?.harness?.isCarrier) {
	            return true;
	        }
	        if (object.wire?.strategyMetadata?.harness?.isCarrier) {
	            return true;
	        }
	        return object.endpoints.some((endpoint) => endpoint.dimension === String(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness));
	    }
	    getObjectPositionInDimension(object, dimension) {
	        const endpoint = object.endpoints.find((candidate) => candidate.dimension === dimension);
	        if (endpoint) {
	            return endpoint.position;
	        }
	        if (object.label) {
	            return object.label.position;
	        }
	        return undefined;
	    }
	    positionKey(position) {
	        return `${position[0]}:${position[1]}`;
	    }
	    createDimensionPositionKeyer(combobulator, dimensionName) {
	        const dimension = combobulator.getDimension(dimensionName);
	        return (position) => `${dimension.roundPoint(position[0])}:${dimension.roundPoint(position[1])}`;
	    }
	    edgeKey(edge, positionKey) {
	        const fromKey = positionKey(edge.from);
	        const toKey = positionKey(edge.to);
	        return fromKey < toKey ? `${fromKey}|${toKey}|${edge.internal}` : `${toKey}|${fromKey}|${edge.internal}`;
	    }
	    edgeOtherPosition(edge, position, positionKey) {
	        const key = positionKey(position);
	        if (positionKey(edge.from) === key) {
	            return edge.to;
	        }
	        if (positionKey(edge.to) === key) {
	            return edge.from;
	        }
	        return undefined;
	    }
	    dereferenceLabelName(name) {
	        try {
	            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(name);
	        }
	        catch {
	            return name;
	        }
	    }
	}
	AltiumHarnessLocalNamingRule.AltiumHarnessLocalNamingRule = AltiumHarnessLocalNamingRule$1;
	
	return AltiumHarnessLocalNamingRule;
}

var AltiumLocalNetLabelGroupingRule = {};

var hasRequiredAltiumLocalNetLabelGroupingRule;

function requireAltiumLocalNetLabelGroupingRule () {
	if (hasRequiredAltiumLocalNetLabelGroupingRule) return AltiumLocalNetLabelGroupingRule;
	hasRequiredAltiumLocalNetLabelGroupingRule = 1;
	Object.defineProperty(AltiumLocalNetLabelGroupingRule, "__esModule", { value: true });
	AltiumLocalNetLabelGroupingRule.AltiumLocalNetLabelGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	class AltiumLocalNetLabelGroupingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, hierarchy) {
	        this.mergeLocalNetLabelsRecursively(manager, hierarchy);
	        return manager;
	    }
	    mergeLocalNetLabelsRecursively(manager, hierarchy) {
	        this.mergeLocalNetLabels(manager, hierarchy.instance);
	        for (const child of hierarchy.children) {
	            this.mergeLocalNetLabelsRecursively(manager, child);
	        }
	    }
	    mergeLocalNetLabels(manager, instance) {
	        const groupsByName = new Map();
	        const allGroups = new Set();
	        const labels = manager.findGroupsWithLabel('net-label', undefined, instance).flatMap((group) => group
	            .getNetLabels(instance)
	            .filter((label) => label.labelType === 'net-label' &&
	            label.strategyMetadata?.hiddenNetName === undefined &&
	            !label.strategyMetadata?.harness?.isCarrier &&
	            Boolean(label.originalLabel.trim()))
	            .map((label) => ({ group, label })));
	        const busRangeMemberNames = new Set(labels
	            .filter(({ label }) => label.strategyMetadata?.busRangeMember)
	            .map(({ label }) => label.originalLabel.toLowerCase()));
	        const addGroup = (key, group) => {
	            if (!groupsByName.has(key)) {
	                groupsByName.set(key, new Set());
	            }
	            const groups = groupsByName.get(key);
	            if (groups === undefined) {
	                throw new Error(`Missing local net-label group for ${key}`);
	            }
	            groups.add(group);
	            allGroups.add(group);
	        };
	        for (const { group, label } of labels) {
	            addGroup(`normal:${JSON.stringify([
	                (label.formattedLabel ?? label.originalLabel).toLowerCase(),
	                label.originalLabel.toLowerCase(),
	            ])}`, group);
	            const formattedName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.formattedLabel ?? label.originalLabel).toLowerCase();
	            if (label.strategyMetadata?.busRangeMember) {
	                addGroup(`bus-member:${label.originalLabel.toLowerCase()}`, group);
	            }
	            const isRepeatedSheetEntryIsland = group
	                .getNetLabels(instance)
	                .some((candidate) => candidate.labelType === 'sheet-entry' &&
	                candidate.strategyMetadata?.['sheet-entry-metadata']?.isRepeated === true);
	            if (isRepeatedSheetEntryIsland && busRangeMemberNames.has(formattedName)) {
	                addGroup(`bus-member:${formattedName}`, group);
	            }
	        }
	        const parent = new Map();
	        const find = (group) => {
	            const currentParent = parent.get(group);
	            if (!currentParent) {
	                parent.set(group, group);
	                return group;
	            }
	            if (currentParent === group) {
	                return group;
	            }
	            const root = find(currentParent);
	            parent.set(group, root);
	            return root;
	        };
	        const union = (a, b) => {
	            const rootA = find(a);
	            const rootB = find(b);
	            if (rootA !== rootB) {
	                parent.set(rootB, rootA);
	            }
	        };
	        for (const groups of groupsByName.values()) {
	            const groupList = Array.from(groups);
	            for (let i = 1; i < groupList.length; i++) {
	                union(groupList[0], groupList[i]);
	            }
	        }
	        const groupsByRoot = new Map();
	        for (const group of allGroups) {
	            const root = find(group);
	            if (!groupsByRoot.has(root)) {
	                groupsByRoot.set(root, []);
	            }
	            const rootGroups = groupsByRoot.get(root);
	            if (rootGroups === undefined) {
	                throw new Error(`Missing local net-label root group`);
	            }
	            rootGroups.push(group);
	        }
	        for (const groups of groupsByRoot.values()) {
	            if (groups.length > 1) {
	                manager.mergeGroups(...groups);
	            }
	        }
	    }
	}
	AltiumLocalNetLabelGroupingRule.AltiumLocalNetLabelGroupingRule = AltiumLocalNetLabelGroupingRule$1;
	
	return AltiumLocalNetLabelGroupingRule;
}

var AltiumHiddenNetNameGroupingRule = {};

var hasRequiredAltiumHiddenNetNameGroupingRule;

function requireAltiumHiddenNetNameGroupingRule () {
	if (hasRequiredAltiumHiddenNetNameGroupingRule) return AltiumHiddenNetNameGroupingRule;
	hasRequiredAltiumHiddenNetNameGroupingRule = 1;
	Object.defineProperty(AltiumHiddenNetNameGroupingRule, "__esModule", { value: true });
	AltiumHiddenNetNameGroupingRule.AltiumHiddenNetNameGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const project_1 = requireProject();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	class AltiumHiddenNetNameGroupingRule$1 {
	    constructor(config) {
	        this.config = config;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        const globalScope = Symbol('global-hidden-power-scope');
	        const isStrictHierarchical = this.config.mode === project_1.HierarchyMode.StrictHierarchical;
	        const groupsByName = new Map();
	        for (const group of manager.findGroupsWithLabel('net-label')) {
	            for (const [instance, nets] of group.getNets()) {
	                for (const net of nets) {
	                    for (const label of net.labels) {
	                        const hiddenNetName = label.strategyMetadata?.hiddenNetName;
	                        if (label.labelType !== 'net-label' || hiddenNetName === undefined) {
	                            continue;
	                        }
	                        const key = hiddenNetName.toLowerCase();
	                        const entry = groupsByName.get(key) ?? { groups: new Set(), scopes: new Set() };
	                        entry.groups.add(group);
	                        entry.scopes.add(isStrictHierarchical ? instance : globalScope);
	                        groupsByName.set(key, entry);
	                    }
	                }
	            }
	        }
	        for (const group of manager.findGroupsWithLabel('power-port')) {
	            for (const [instance, nets] of group.getNets()) {
	                for (const net of nets) {
	                    for (const label of net.labels) {
	                        if (label.labelType !== 'power-port' || label.formattedLabel === undefined) {
	                            continue;
	                        }
	                        const scope = isStrictHierarchical ? instance : globalScope;
	                        const formattedName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.formattedLabel);
	                        if (formattedName.toLowerCase() !== label.originalLabel.toLowerCase()) {
	                            continue;
	                        }
	                        const entry = groupsByName.get(formattedName.toLowerCase());
	                        if (entry?.scopes.has(scope)) {
	                            entry.groups.add(group);
	                        }
	                    }
	                }
	            }
	        }
	        for (const { groups } of groupsByName.values()) {
	            const [firstGroup] = groups;
	            if (firstGroup === undefined) {
	                continue;
	            }
	            manager.mergeGroups(...groups);
	        }
	        return manager;
	    }
	}
	AltiumHiddenNetNameGroupingRule.AltiumHiddenNetNameGroupingRule = AltiumHiddenNetNameGroupingRule$1;
	
	return AltiumHiddenNetNameGroupingRule;
}

var AltiumNetNameCollisionFinalizingRule = {};

var hasRequiredAltiumNetNameCollisionFinalizingRule;

function requireAltiumNetNameCollisionFinalizingRule () {
	if (hasRequiredAltiumNetNameCollisionFinalizingRule) return AltiumNetNameCollisionFinalizingRule;
	hasRequiredAltiumNetNameCollisionFinalizingRule = 1;
	Object.defineProperty(AltiumNetNameCollisionFinalizingRule, "__esModule", { value: true });
	AltiumNetNameCollisionFinalizingRule.AltiumNetNameCollisionFinalizingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	const compareAltiumPinReferences_1 = requireCompareAltiumPinReferences();
	class AltiumNetNameCollisionFinalizingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Finalize;
	    }
	    apply(groups, _hierarchy) {
	        const groupsByName = new Map();
	        for (const group of groups) {
	            if (!group.physicalName) {
	                continue;
	            }
	            const sameNameGroups = groupsByName.get(group.physicalName) ?? [];
	            sameNameGroups.push(group);
	            groupsByName.set(group.physicalName, sameNameGroups);
	        }
	        for (const [name, sameNameGroups] of groupsByName) {
	            if (sameNameGroups.length < 2 || !sameNameGroups.some((group) => this.hasNetLabelNamed(group, name))) {
	                continue;
	            }
	            for (const group of sameNameGroups) {
	                if (this.hasNetLabelNamed(group, name)) {
	                    continue;
	                }
	                if (!this.hasSheetEntryNamed(group, name)) {
	                    continue;
	                }
	                const fallbackName = this.getComponentConnectionName(group);
	                if (fallbackName) {
	                    group.physicalName = fallbackName;
	                }
	            }
	        }
	    }
	    hasNetLabelNamed(group, name) {
	        return group.getNetLabels().some((label) => {
	            if (label.labelType !== 'net-label') {
	                return false;
	            }
	            const labelName = label.formattedLabel ?? label.originalLabel;
	            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(labelName) === name;
	        });
	    }
	    hasSheetEntryNamed(group, name) {
	        return group.getNetLabels().some((label) => {
	            if (label.labelType !== 'sheet-entry') {
	                return false;
	            }
	            if (label.strategyMetadata?.harness?.harnessSource === 'synthetic-sheet-entry') {
	                return false;
	            }
	            const labelName = label.formattedLabel ?? label.originalLabel;
	            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(labelName) === name;
	        });
	    }
	    getComponentConnectionName(group) {
	        const connections = [];
	        for (const [_instance, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    connections.push({
	                        moduleName: conn.connection.moduleName,
	                        pinRef: conn.connection.pinRef,
	                    });
	                }
	            }
	        }
	        if (connections.length === 0) {
	            return undefined;
	        }
	        connections.sort((a, b) => {
	            const moduleCompare = sigma_engine_shared_1.Utils.naturalStringSort(a.moduleName, b.moduleName);
	            if (moduleCompare !== 0) {
	                return moduleCompare;
	            }
	            return (0, compareAltiumPinReferences_1.compareAltiumPinReferences)(a.pinRef, b.pinRef);
	        });
	        const firstConn = connections[0];
	        return `Net${firstConn.moduleName}_${firstConn.pinRef}`;
	    }
	}
	AltiumNetNameCollisionFinalizingRule.AltiumNetNameCollisionFinalizingRule = AltiumNetNameCollisionFinalizingRule$1;
	
	return AltiumNetNameCollisionFinalizingRule;
}

var hasRequiredAltiumNetNamingStrategy;

function requireAltiumNetNamingStrategy () {
	if (hasRequiredAltiumNetNamingStrategy) return AltiumNetNamingStrategy;
	hasRequiredAltiumNetNamingStrategy = 1;
	Object.defineProperty(AltiumNetNamingStrategy, "__esModule", { value: true });
	AltiumNetNamingStrategy.AltiumNetNamingStrategy = void 0;
	AltiumNetNamingStrategy.formatLocalNetNameWithSheetNumber = formatLocalNetNameWithSheetNumber;
	AltiumNetNamingStrategy.appliesChannelNetFormatting = appliesChannelNetFormatting;
	const sigma_engine_shared_1 = requireDist$1();
	const NetCombobulator_1 = requireNetCombobulator();
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const FallbackNetNamingRule_1 = requireFallbackNetNamingRule();
	const NetNamingRuleChain_1 = requireNetNamingRuleChain();
	const AltiumHierarchicalGroupingRule_1 = requireAltiumHierarchicalGroupingRule();
	const AltiumPowerPortGroupingRule_1 = requireAltiumPowerPortGroupingRule();
	const AltiumNetLabelGroupingRule_1 = requireAltiumNetLabelGroupingRule();
	const AltiumPortGroupingRule_1 = requireAltiumPortGroupingRule();
	const AltiumOffSheetConnectorGroupingRule_1 = requireAltiumOffSheetConnectorGroupingRule();
	const AltiumHierarchyPriorityNamingRule_1 = requireAltiumHierarchyPriorityNamingRule();
	const AltiumPowerPortNamingRule_1 = requireAltiumPowerPortNamingRule();
	const AltiumNetLabelNamingRule_1 = requireAltiumNetLabelNamingRule();
	const AltiumOffSheetConnectorNamingRule_1 = requireAltiumOffSheetConnectorNamingRule();
	const AltiumPortNamingRule_1 = requireAltiumPortNamingRule();
	const AltiumSheetEntryNamingRule_1 = requireAltiumSheetEntryNamingRule();
	const AltiumNamingPinNamingRule_1 = requireAltiumNamingPinNamingRule();
	const AltiumComponentConnectionNamingRule_1 = requireAltiumComponentConnectionNamingRule();
	const IndividualGroupingRule_1 = requireIndividualGroupingRule();
	const AltiumNameGenerator_1 = requireAltiumNameGenerator();
	const sigma_engine_shared_2 = requireDist$1();
	const AltiumSheetEntryLocalRepeatLocalNamingRule_1 = requireAltiumSheetEntryLocalRepeatLocalNamingRule();
	const AltiumLabelTypeNameUtil_1 = requireAltiumLabelTypeNameUtil();
	const AltiumPowerPortLocalNamingRule_1 = requireAltiumPowerPortLocalNamingRule();
	const AltiumHarnessLocalNamingRule_1 = requireAltiumHarnessLocalNamingRule();
	const AltiumLocalNetLabelGroupingRule_1 = requireAltiumLocalNetLabelGroupingRule();
	const AltiumHiddenNetNameGroupingRule_1 = requireAltiumHiddenNetNameGroupingRule();
	const AltiumNetNameCollisionFinalizingRule_1 = requireAltiumNetNameCollisionFinalizingRule();
	const AltiumSheetEntryNamingEligibility_1 = requireAltiumSheetEntryNamingEligibility();
	const SchDocTypes_1 = requireSchDocTypes();
	function formatLocalNetNameWithSheetNumber(baseName, sheetNumber, differentialPairSuffixes = ['_P', '_N']) {
	    const terminalDifferentialSuffix = differentialPairSuffixes
	        .filter((suffix) => suffix.length > 0)
	        .sort((a, b) => b.length - a.length)
	        .find((suffix) => {
	        if (!baseName.toLowerCase().endsWith(suffix.toLowerCase())) {
	            return false;
	        }
	        return suffix.startsWith('_');
	    });
	    if (terminalDifferentialSuffix) {
	        return `${baseName.slice(0, -terminalDifferentialSuffix.length)}_${sheetNumber}${baseName.slice(-terminalDifferentialSuffix.length)}`;
	    }
	    return `${baseName}_${sheetNumber}`;
	}
	function appliesChannelNetFormatting(instance) {
	    const inheritedPhysicalContextParent = instance.inheritsPhysicalNamingContext ? instance.path.at(-1) : undefined;
	    const numInstances = instance.inheritsPhysicalNamingContext ? 1 : instance.numInstances;
	    const totalSheetInstances = instance.inheritsPhysicalNamingContext ?
	        (inheritedPhysicalContextParent?.totalSheetInstances ?? instance.totalSheetInstances)
	        : instance.totalSheetInstances;
	    const designWideInstances = inheritedPhysicalContextParent ? inheritedPhysicalContextParent.designWideInstances : instance.designWideInstances;
	    return numInstances !== 1 || (designWideInstances ?? totalSheetInstances) !== 1;
	}
	class AltiumNetNamingStrategy$1 {
	    constructor(projectConfigUtil, nameGenerator, hierarchyMode, options = {}) {
	        this.projectConfigUtil = projectConfigUtil;
	        this.nameGenerator = nameGenerator;
	        const channelRoomNamingStyle = this.projectConfigUtil.getDesignConfiguration('ChannelRoomNamingStyle');
	        this.config = {
	            mode: hierarchyMode,
	            allowPortNetNames: this.projectConfigUtil.getDesignConfigurationBoolean('AllowPortNetNames'),
	            allowSheetEntryNetNames: this.projectConfigUtil.getDesignConfigurationBoolean('AllowSheetEntryNetNames'),
	            appendSheetNumberToLocalNets: this.projectConfigUtil.getDesignConfigurationBoolean('AppendSheetNumberToLocalNets'),
	            powerPortPriority: this.projectConfigUtil.getDesignConfiguration('PowerPortNamesTakePriority'),
	            higherLevelNamesTakePriority: this.projectConfigUtil.getDesignConfiguration('NameNetsHierarchically'),
	            channelRoomNamingStyle: channelRoomNamingStyle ?? altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatNumericWithNames,
	            differentialPairSuffixes: this.projectConfigUtil.getDifferentialPairSuffixes(),
	        };
	        this.ruleChain = this.buildRuleChain(options.enableDebug ?? false);
	    }
	    transformNetName(netObject, instance, localNet) {
	        const namingSource = this.getNamingSource(netObject, localNet !== undefined);
	        if (!namingSource) {
	            return undefined;
	        }
	        return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(namingSource.labelType, ...this.applyNamingRules(namingSource.baseName, namingSource.labelType, instance, namingSource.strategyMetadata, localNet));
	    }
	    transformConnectivityName(netObject, instance, localNet) {
	        const namingSource = this.getNamingSource(netObject, localNet !== undefined);
	        if (!namingSource ||
	            !this.shouldUseSchematicLocalConnectivityName(namingSource.labelType, namingSource.strategyMetadata)) {
	            return undefined;
	        }
	        return this.connectivityName(namingSource.baseName, namingSource.labelType, instance, namingSource.strategyMetadata, localNet);
	    }
	    canLabelTypeNameNets(labelType, _instance) {
	        switch (labelType) {
	            case 'port':
	                return this.config.allowPortNetNames;
	            case 'sheet-entry':
	                return this.config.allowSheetEntryNetNames;
	            case 'power-port':
	                return true;
	            case 'net-label':
	                return true;
	            default:
	                return true;
	        }
	    }
	    resolveNetName(net, instance) {
	        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.Yes) {
	            const powerLabel = net.labels.find((label) => label.canNameNet && label.labelType === 'power-port', AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly);
	            if (powerLabel) {
	                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('power-port', this.labelName(powerLabel, instance));
	            }
	        }
	        if (this.config.allowPortNetNames) {
	            const portLabel = net.labels.find((label) => label.canNameNet &&
	                label.labelType === 'port' &&
	                label.strategyMetadata?.harness?.harnessSource !== 'synthetic-port');
	            if (portLabel) {
	                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('port', this.labelName(portLabel, instance));
	            }
	        }
	        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.No) {
	            const powerLabel = net.labels.find((label) => label.canNameNet && label.labelType === 'power-port', AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly);
	            if (powerLabel) {
	                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('power-port', this.labelName(powerLabel, instance));
	            }
	        }
	        const netLabel = net.labels.find((label) => label.canNameNet && label.labelType === 'net-label');
	        if (netLabel) {
	            if (this.hasRepeatedSheetEntry(net)) {
	                const physicalName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(netLabel.formattedLabel ?? netLabel.originalLabel, 'net-label'));
	                if (!this.shouldUseSchematicLocalConnectivityName('net-label', netLabel.strategyMetadata)) {
	                    return physicalName;
	                }
	                const authoredName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(netLabel.originalLabel, 'net-label'));
	                return physicalName === authoredName ? physicalName : `CONNECTIVITY_NAME(${physicalName},${authoredName})`;
	            }
	            if (this.config.appendSheetNumberToLocalNets && !appliesChannelNetFormatting(instance)) {
	                const authoredNetLabel = { originalLabel: netLabel.originalLabel };
	                const localPhysicalName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', ...this.applyNamingRules(authoredNetLabel, 'net-label', instance, netLabel.strategyMetadata, net));
	                netLabel.formattedLabel = localPhysicalName;
	                if (this.shouldUseSchematicLocalConnectivityName('net-label', netLabel.strategyMetadata)) {
	                    return this.connectivityName(authoredNetLabel, 'net-label', instance, netLabel.strategyMetadata, net);
	                }
	                return localPhysicalName;
	            }
	            if (this.shouldUseSchematicLocalConnectivityName('net-label', netLabel.strategyMetadata)) {
	                return this.connectivityName(netLabel, 'net-label', instance, netLabel.strategyMetadata, net);
	            }
	            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', this.labelName(netLabel, instance));
	        }
	        const namingConnection = net.connections.find((conn) => conn.connection.appliesNetName);
	        if (namingConnection?.connection.appliesNetName) {
	            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('NamingPin', namingConnection.connection.appliesNetName.label);
	        }
	        if (net.connections.length > 0) {
	            const firstConn = net.connections[0];
	            const moduleName = firstConn.connection.moduleName;
	            const moduleRef = firstConn.connection.moduleRef;
	            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('ComponentConnection', `Net${moduleName}_${firstConn.connection.pinRef}`, moduleRef.length > 0 ? moduleRef : undefined);
	        }
	        if (this.config.allowSheetEntryNetNames) {
	            const sheetEntryLabel = net.labels.find((label) => label.canNameNet &&
	                label.labelType === 'sheet-entry' &&
	                label.strategyMetadata?.harness?.harnessSource !== 'synthetic-sheet-entry');
	            if (sheetEntryLabel) {
	                const strategyMetadata = sheetEntryLabel.strategyMetadata;
	                if (!strategyMetadata || !strategyMetadata['sheet-entry-metadata']) {
	                    throw new Error(`Sheet entry label is missing metadata`);
	                }
	                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('sheet-entry', this.labelName(sheetEntryLabel, instance, 'sheet-entry'), strategyMetadata['sheet-entry-metadata'].sheetSymbolUniqueId);
	            }
	        }
	        return `NET_UNKNOWN_${sigma_engine_shared_2.Utils.randomAlphaStr(10)}`;
	    }
	    labelName(label, instance, ensureType, componentSuffix, selectionMode = AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOrChannel, localNetSheetNumber) {
	        const derefed = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.formattedLabel ?? label.originalLabel, ensureType);
	        const useComponentName = localNetSheetNumber === undefined ?
	            `${derefed}${componentSuffix ?? ''}`
	            : formatLocalNetNameWithSheetNumber(derefed, localNetSheetNumber, this.config.differentialPairSuffixes?.length ? this.config.differentialPairSuffixes : undefined);
	        if (selectionMode === AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly) {
	            return useComponentName;
	        }
	        const inheritedPhysicalContextParent = instance.inheritsPhysicalNamingContext ? instance.path.at(-1) : undefined;
	        const roomName = instance.inheritsPhysicalNamingContext ? (instance.physicalRoomName ?? instance.roomName) : instance.roomName;
	        const channelIndex = instance.inheritsPhysicalNamingContext ?
	            (instance.physicalChannelIndex ?? instance.channelIndex)
	            : instance.channelIndex;
	        const physicalChannelIndex = instance.physicalChannelIndex ?? instance.channelIndex;
	        const channelPrefix = instance.inheritsPhysicalNamingContext ?
	            (instance.physicalChannelPrefix ?? instance.channelPrefix)
	            : instance.channelPrefix;
	        return this.nameGenerator.generateName(useComponentName, {
	            name: roomName,
	            filename: instance.hierarchyEntry.sourceFile,
	            uniqueId: instance.hierarchyEntry.uniqueId ?? '<unknown-uniqueid>',
	            channelIndex,
	            physicalChannelIndex,
	            roomName,
	            channelPrefix,
	            pathUniqueId: instance.pathUniqueId,
	            isMultiChannel: instance.isMultiChannel,
	            numInstances: instance.inheritsPhysicalNamingContext ? 1 : instance.numInstances,
	            totalSheetInstances: instance.inheritsPhysicalNamingContext ?
	                (inheritedPhysicalContextParent?.totalSheetInstances ?? instance.totalSheetInstances)
	                : instance.totalSheetInstances,
	            designWideInstances: inheritedPhysicalContextParent ?
	                inheritedPhysicalContextParent.designWideInstances
	                : instance.designWideInstances,
	        }, {}, selectionMode, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical, AltiumNameGenerator_1.NameGeneratorNameType.Net);
	    }
	    formatChannelLocalPowerPortName(label, instance) {
	        return this.nameGenerator.generateName(AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.originalLabel), {
	            name: instance.roomName,
	            filename: instance.hierarchyEntry.sourceFile,
	            uniqueId: instance.hierarchyEntry.uniqueId ?? '<unknown-uniqueid>',
	            channelIndex: instance.channelIndex,
	            physicalChannelIndex: instance.physicalChannelIndex ?? instance.channelIndex,
	            roomName: instance.roomName,
	            channelPrefix: instance.channelPrefix,
	            pathUniqueId: instance.pathUniqueId,
	            isMultiChannel: instance.isMultiChannel,
	            numInstances: instance.numInstances,
	            totalSheetInstances: instance.totalSheetInstances,
	        }, {}, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOrChannel, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical, AltiumNameGenerator_1.NameGeneratorNameType.Net);
	    }
	    getNamingSource(netObject, useOriginalLabel = false) {
	        if (netObject.label?.canNameNet) {
	            return {
	                baseName: useOriginalLabel ? { originalLabel: netObject.label.originalLabel } : netObject.label,
	                labelType: netObject.label.labelType,
	                strategyMetadata: netObject.label.strategyMetadata,
	            };
	        }
	        if (netObject.connection?.connection.appliesNetName) {
	            return {
	                baseName: { originalLabel: netObject.connection.connection.appliesNetName.label },
	                labelType: netObject.connection.connection.appliesNetName.labelType,
	                strategyMetadata: netObject.connection.connection.appliesNetName.strategyMetadata,
	            };
	        }
	        return undefined;
	    }
	    shouldUseSchematicLocalConnectivityName(labelType, strategyMetadata) {
	        return (labelType === 'net-label' &&
	            !strategyMetadata?.harness?.preservePhysicalName &&
	            (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || this.config.mode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical));
	    }
	    resolvePhysicalNetNames(hierarchy) {
	        return this.ruleChain.execute(hierarchy);
	    }
	    getNetNamingDebug() {
	        const debug = this.ruleChain.exportDebugInfo();
	        if (!debug) {
	            return undefined;
	        }
	        return {
	            enabled: true,
	            snapshots: debug.snapshots,
	            trackedObjects: Object.fromEntries(debug.trackedObjects),
	        };
	    }
	    createVirtualRootHierarchy(roots) {
	        if (roots.length === 0) {
	            throw new Error('createVirtualRootHierarchy requires at least one root');
	        }
	        const firstChildInstance = roots[0].instance;
	        const virtualInstance = {
	            instanceId: '__virtual_root__',
	            hierarchyEntry: firstChildInstance.hierarchyEntry,
	            path: [],
	            children: roots.map((c) => c.instance),
	            pathUniqueId: '__virtual_root__',
	            sheetSymbol: undefined,
	            numInstances: 1,
	            totalSheetInstances: 1,
	            instanceIndex: 0,
	            channelIndex: 0,
	            channelPrefix: '',
	            roomName: '',
	            isMultiChannel: false,
	        };
	        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this, virtualInstance, {
	            autoAssignNets: false,
	            pointRoundingPrecision: 0.1,
	        });
	        return {
	            pathUniqueId: '__virtual_root__',
	            hierarchyDepth: -1,
	            instance: virtualInstance,
	            combobulator: emptyCombobulator,
	            children: roots,
	        };
	    }
	    buildRuleChain(enableDebug) {
	        const chain = new NetNamingRuleChain_1.NetNamingRuleChain(enableDebug);
	        chain.add(new AltiumHarnessLocalNamingRule_1.AltiumHarnessLocalNamingRule(this.config, this));
	        chain.add(new AltiumSheetEntryLocalRepeatLocalNamingRule_1.AltiumSheetEntryLocalRepeatLocalNamingRule(this.config, this));
	        chain.add(new AltiumPowerPortLocalNamingRule_1.AltiumPowerPortLocalNamingRule(this.config, this));
	        chain.add(new IndividualGroupingRule_1.IndividualGroupingRule());
	        this.addGroupingRules(chain);
	        this.addNamingRules(chain);
	        return chain;
	    }
	    addGroupingRules(chain) {
	        chain.add(new AltiumLocalNetLabelGroupingRule_1.AltiumLocalNetLabelGroupingRule());
	        chain.add(new AltiumHiddenNetNameGroupingRule_1.AltiumHiddenNetNameGroupingRule(this.config));
	        if (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || this.config.mode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
	            chain.add(new AltiumHierarchicalGroupingRule_1.AltiumHierarchicalGroupingRule(this.config));
	        }
	        chain.add(new AltiumPowerPortGroupingRule_1.AltiumPowerPortGroupingRule(this.config));
	        if (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Global) {
	            chain.add(new AltiumNetLabelGroupingRule_1.AltiumNetLabelGroupingRule(this.config));
	        }
	        if (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Flat || this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Global) {
	            chain.add(new AltiumPortGroupingRule_1.AltiumPortGroupingRule(this.config));
	        }
	        chain.add(new AltiumOffSheetConnectorGroupingRule_1.AltiumOffSheetConnectorGroupingRule(this.config));
	    }
	    addNamingRules(chain) {
	        if (this.config.higherLevelNamesTakePriority) {
	            chain.add(new AltiumHierarchyPriorityNamingRule_1.AltiumHierarchyPriorityNamingRule(this.config, this));
	        }
	        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.Yes) {
	            chain.add(new AltiumPowerPortNamingRule_1.AltiumPowerPortNamingRule(this.config, this));
	        }
	        chain.add(new AltiumNetLabelNamingRule_1.AltiumNetLabelNamingRule(this.config, this));
	        chain.add(new AltiumNamingPinNamingRule_1.AltiumNamingPinNamingRule(this.config, this));
	        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.No) {
	            chain.add(new AltiumPowerPortNamingRule_1.AltiumPowerPortNamingRule(this.config, this));
	        }
	        chain.add(new AltiumSheetEntryNamingRule_1.AltiumSheetEntryNamingRule(this.config, this));
	        chain.add(new AltiumOffSheetConnectorNamingRule_1.AltiumOffSheetConnectorNamingRule(this.config, this));
	        chain.add(new AltiumPortNamingRule_1.AltiumPortNamingRule(this.config, this));
	        chain.add(new AltiumComponentConnectionNamingRule_1.AltiumComponentConnectionNamingRule(this.config, this));
	        chain.add(new FallbackNetNamingRule_1.FallbackNetNamingRule('NET_UNKNOWN'));
	        chain.add(new AltiumNetNameCollisionFinalizingRule_1.AltiumNetNameCollisionFinalizingRule());
	    }
	    applyNamingRules(baseName, labelType, instance, strategyMetadata, localNet) {
	        if (labelType === 'net-label' && strategyMetadata?.harness?.preservePhysicalName) {
	            return [
	                this.labelName(baseName, instance, labelType, undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly),
	            ];
	        }
	        if (labelType === 'power-port' && this.config.mode !== altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
	            return [
	                this.labelName(baseName, instance, labelType, undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly),
	            ];
	        }
	        const localNetSheetNumber = this.shouldAppendSheetNumberToLocalNetLabel(labelType, instance, localNet) ?
	            this.getLocalNetSheetNumber(instance)
	            : undefined;
	        const netName = this.labelName(baseName, instance, labelType, undefined, undefined, localNetSheetNumber);
	        if (labelType === 'sheet-entry') {
	            return [netName, strategyMetadata?.['sheet-entry-metadata']?.sheetSymbolUniqueId];
	        }
	        return [netName];
	    }
	    connectivityName(baseName, labelType, instance, strategyMetadata, localNet) {
	        const physicalName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(labelType, ...this.applyNamingRules(baseName, labelType, instance, strategyMetadata, localNet));
	        const localName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(labelType, ...this.applyConnectivityNamingRules(baseName, labelType, instance, strategyMetadata, localNet));
	        if (physicalName === localName) {
	            return localName;
	        }
	        return `CONNECTIVITY_NAME(${physicalName},${localName})`;
	    }
	    applyConnectivityNamingRules(baseName, labelType, instance, _strategyMetadata, localNet) {
	        const localNetSheetNumber = this.shouldAppendSheetNumberToLocalNetLabel(labelType, instance, localNet) ?
	            this.getLocalNetSheetNumber(instance)
	            : undefined;
	        const netName = this.labelName({ originalLabel: baseName.originalLabel }, instance, labelType, undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly, localNetSheetNumber);
	        return [netName];
	    }
	    getHierarchicalNetLabelName(label, localNet, instance, group) {
	        const formattedName = label.formattedLabel ?
	            AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.formattedLabel)
	            : label.originalLabel;
	        if (group.hasLabelType('port') &&
	            group
	                .getNetLabels()
	                .some((candidate) => candidate.labelType === 'net-label' &&
	                candidate.strategyMetadata?.busRangeMember === true &&
	                candidate.originalLabel.toLowerCase() === label.originalLabel.toLowerCase())) {
	            return label.originalLabel;
	        }
	        if (formattedName !== label.originalLabel &&
	            group
	                .getNetLabels()
	                .some((candidate) => candidate.labelType === 'net-label' &&
	                candidate.strategyMetadata?.busRangeMember === true &&
	                candidate.originalLabel.toLowerCase() === formattedName.toLowerCase())) {
	            return formattedName;
	        }
	        if (this.isNonLocalConnectedLocalNetLabel(localNet, instance) ||
	            this.hasGroupReachableIdentityPortFromChildLabel(label, localNet, instance, group)) {
	            return this.labelName({ originalLabel: label.originalLabel }, instance, 'net-label', undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly);
	        }
	        return label.formattedLabel ?? label.originalLabel;
	    }
	    getNetLabelNameForNamingGroup(label, instance, group) {
	        if (group.hasLabelType('port') &&
	            group
	                .getNetLabels()
	                .some((candidate) => candidate.labelType === 'net-label' &&
	                candidate.strategyMetadata?.busRangeMember === true &&
	                candidate.originalLabel.toLowerCase() === label.originalLabel.toLowerCase())) {
	            return label.originalLabel;
	        }
	        if (this.config.appendSheetNumberToLocalNets &&
	            !appliesChannelNetFormatting(instance) &&
	            this.hasPowerPortOnInstance(instance, group)) {
	            return label.originalLabel;
	        }
	        return label.formattedLabel ?? label.originalLabel;
	    }
	    hasPowerPortOnInstance(instance, group) {
	        return (group.getNets().get(instance) ?? []).some((net) => net.labels.some((label) => label.labelType === 'power-port'));
	    }
	    shouldAppendSheetNumberToLocalNetLabel(labelType, instance, localNet) {
	        return (this.config.appendSheetNumberToLocalNets &&
	            labelType === 'net-label' &&
	            !appliesChannelNetFormatting(instance) &&
	            !this.hasRepeatedSheetEntry(localNet) &&
	            !localNet?.labels.some((label) => label.labelType === 'port' || label.labelType === 'power-port'));
	    }
	    getLocalNetSheetNumber(instance) {
	        if (this.projectConfigUtil.getDesignConfigurationBoolean('AutoSheetNumbering')) {
	            return this.projectConfigUtil.getDesignConfigurationBoolean('ReorderDocumentsOnCompile') ?
	                instance.compiledSheetNumber
	                : instance.hierarchyEntry.sheetNumber;
	        }
	        const schematic = instance.hierarchyEntry.schematic;
	        if (!schematic || typeof schematic.getChildRecords !== 'function') {
	            return instance.hierarchyEntry.sheetNumber;
	        }
	        const sheetNumberParameter = schematic
	            .getChildRecords(-1)
	            .filter((record) => record.recordType === SchDocTypes_1.RecordType.PARAMETER)
	            .find((record) => record.name.toLowerCase() === 'sheetnumber');
	        const value = sheetNumberParameter?.text.trim();
	        if (!value || value === '*') {
	            return undefined;
	        }
	        const sheetNumber = Number(value);
	        return Number.isInteger(sheetNumber) && sheetNumber > 0 ? sheetNumber : undefined;
	    }
	    hasRepeatedSheetEntry(localNet) {
	        return (localNet?.labels.some((label) => label.labelType === 'sheet-entry' && label.strategyMetadata?.['sheet-entry-metadata']?.isRepeated) ?? false);
	    }
	    hasGroupReachableIdentityPortFromChildLabel(winningLabel, winningNet, instance, group) {
	        if (instance.path.length === 0) {
	            return false;
	        }
	        for (const [portInstance, nets] of group.getNets()) {
	            if (portInstance !== instance && !instance.path.includes(portInstance)) {
	                continue;
	            }
	            for (const net of nets) {
	                if (net === winningNet) {
	                    continue;
	                }
	                for (const port of net.labels) {
	                    if (port.labelType === 'port' &&
	                        (0, AltiumSheetEntryNamingEligibility_1.isAltiumPairedChildPortNamingEligible)(group, port, this.config.mode) &&
	                        this.isHierarchicalIdentityPort(winningLabel, port)) {
	                        return true;
	                    }
	                }
	            }
	        }
	        return false;
	    }
	    isHierarchicalIdentityPort(winningLabel, port) {
	        if (winningLabel.originalLabel.toLowerCase() === port.originalLabel.toLowerCase()) {
	            return true;
	        }
	        const portMemberName = port.strategyMetadata?.harness?.harnessMemberName;
	        if (!portMemberName) {
	            return false;
	        }
	        const winningMemberName = winningLabel.strategyMetadata?.harness?.harnessMemberName ?? winningLabel.originalLabel.split('.').at(-1);
	        return winningMemberName?.toLowerCase() === portMemberName.toLowerCase();
	    }
	    isNonLocalConnectedLocalNetLabel(localNet, instance) {
	        return (this.config.appendSheetNumberToLocalNets &&
	            !appliesChannelNetFormatting(instance) &&
	            localNet.labels.some((label) => label.labelType === 'port' || label.labelType === 'power-port'));
	    }
	}
	AltiumNetNamingStrategy.AltiumNetNamingStrategy = AltiumNetNamingStrategy$1;
	
	return AltiumNetNamingStrategy;
}

var AltiumFontMapper = {};

var hasRequiredAltiumFontMapper;

function requireAltiumFontMapper () {
	if (hasRequiredAltiumFontMapper) return AltiumFontMapper;
	hasRequiredAltiumFontMapper = 1;
	Object.defineProperty(AltiumFontMapper, "__esModule", { value: true });
	AltiumFontMapper.AltiumFontMapper = void 0;
	const altiumValidation_1 = requireAltiumValidation();
	class AltiumFontMapper$1 {
	    static { this.RasterSizeCorrection = 1.1; }
	    constructor(projectDef) {
	        this.schematicFontMap = new Map();
	        this.projectFonts = new Map();
	        this.fontKeyToId = new Map();
	        for (const [fileName, schematicDef] of Object.entries(projectDef.schematics)) {
	            if (!schematicDef.validationResult?.isValid) {
	                continue;
	            }
	            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
	            const sheet = schematic.getSheet();
	            const localFontMap = new Map();
	            const fonts = sheet.getFonts();
	            for (const [localFontId, fontInfo] of fonts.entries()) {
	                const projectFontId = this.getOrCreateProjectFont(fontInfo);
	                localFontMap.set(localFontId, projectFontId);
	            }
	            this.schematicFontMap.set(fileName, localFontMap);
	        }
	    }
	    getOrCreateProjectFont(fontInfo) {
	        const fontKey = this.createFontKey(fontInfo);
	        const keyString = this.fontKeyToString(fontKey);
	        const existingId = this.fontKeyToId.get(keyString);
	        if (existingId) {
	            return existingId;
	        }
	        const fontId = this.generateFontId(fontKey);
	        const browserFont = this.createBrowserFont(fontInfo);
	        this.fontKeyToId.set(keyString, fontId);
	        this.projectFonts.set(fontId, browserFont);
	        return fontId;
	    }
	    createFontKey(fontInfo) {
	        return {
	            fontName: fontInfo.fontName,
	            size: fontInfo.size,
	            bold: fontInfo.bold ?? false,
	            italic: fontInfo.italic ?? false,
	        };
	    }
	    fontKeyToString(key) {
	        return `${key.fontName}|${key.size}|${key.bold}|${key.italic}`;
	    }
	    generateFontId(key) {
	        let baseName = key.fontName.replace(/\s+/g, '_');
	        if (key.bold) {
	            baseName += '_Bold';
	        }
	        if (key.italic) {
	            baseName += '_Italic';
	        }
	        const sizeStr = `_${key.size.toFixed(2)}mm`;
	        baseName += sizeStr;
	        let fontId = baseName;
	        let counter = 1;
	        while (this.projectFonts.has(fontId)) {
	            fontId = `${baseName}_${counter}`;
	            counter++;
	        }
	        return fontId;
	    }
	    createBrowserFont(fontInfo) {
	        const attrs = fontInfo.textOpts.attr;
	        return {
	            type: 'browser',
	            name: fontInfo.fontName,
	            size: fontInfo.size,
	            rasterSizeCorrection: AltiumFontMapper$1.RasterSizeCorrection,
	            attrs: attrs && attrs.length > 0 ? attrs : undefined,
	        };
	    }
	}
	AltiumFontMapper.AltiumFontMapper = AltiumFontMapper$1;
	
	return AltiumFontMapper;
}

var remediation$1 = {};

var hasRequiredRemediation$1;

function requireRemediation$1 () {
	if (hasRequiredRemediation$1) return remediation$1;
	hasRequiredRemediation$1 = 1;
	Object.defineProperty(remediation$1, "__esModule", { value: true });
	remediation$1.ProjectParseRemediation = remediation$1.ProjectParseFailureCode = remediation$1.NetlistGenerationRemediation = remediation$1.NetlistGenerationFailureCode = remediation$1.RemediationOwner = void 0;
	var RemediationOwner;
	(function (RemediationOwner) {
	    RemediationOwner["Ops"] = "ops";
	    RemediationOwner["Customer"] = "customer";
	    RemediationOwner["Dev"] = "dev";
	})(RemediationOwner || (remediation$1.RemediationOwner = RemediationOwner = {}));
	var NetlistGenerationFailureCode;
	(function (NetlistGenerationFailureCode) {
	    NetlistGenerationFailureCode["AltiumProjectShape"] = "NetlistGeneration_AltiumProjectShape";
	    NetlistGenerationFailureCode["ErcErrors"] = "NetlistGeneration_ErcErrors";
	    NetlistGenerationFailureCode["AltiumGenerationFailed"] = "NetlistGeneration_AltiumGenerationFailed";
	    NetlistGenerationFailureCode["AltiumUnsupportedBuild"] = "NetlistGeneration_AltiumUnsupportedBuild";
	    NetlistGenerationFailureCode["AltiumEmptyNetlist"] = "NetlistGeneration_AltiumEmptyNetlist";
	    NetlistGenerationFailureCode["AltiumPartialNetlist"] = "NetlistGeneration_AltiumPartialNetlist";
	    NetlistGenerationFailureCode["NonNativeNetlist"] = "NetlistGeneration_NonNativeNetlist";
	    NetlistGenerationFailureCode["GuestUnavailable"] = "NetlistGeneration_GuestUnavailable";
	    NetlistGenerationFailureCode["BridgeDead"] = "NetlistGeneration_BridgeDead";
	    NetlistGenerationFailureCode["CommandWedged"] = "NetlistGeneration_CommandWedged";
	    NetlistGenerationFailureCode["NoSession"] = "NetlistGeneration_NoSession";
	    NetlistGenerationFailureCode["BudgetExhausted"] = "NetlistGeneration_BudgetExhausted";
	    NetlistGenerationFailureCode["AltiumNotConfigured"] = "NetlistGeneration_AltiumNotConfigured";
	    NetlistGenerationFailureCode["KicadScriptFailed"] = "NetlistGeneration_KicadScriptFailed";
	    NetlistGenerationFailureCode["NoBuildFiles"] = "NetlistGeneration_NoBuildFiles";
	    NetlistGenerationFailureCode["UnsafePath"] = "NetlistGeneration_UnsafePath";
	    NetlistGenerationFailureCode["DuplicateStagePath"] = "NetlistGeneration_DuplicateStagePath";
	    NetlistGenerationFailureCode["NoToken"] = "NetlistGeneration_NoToken";
	    NetlistGenerationFailureCode["ApiFailed"] = "NetlistGeneration_ApiFailed";
	    NetlistGenerationFailureCode["NoGenerator"] = "NetlistGeneration_NoGenerator";
	    NetlistGenerationFailureCode["BadStubFile"] = "NetlistGeneration_BadStubFile";
	    NetlistGenerationFailureCode["UploadFailed"] = "NetlistGeneration_UploadFailed";
	})(NetlistGenerationFailureCode || (remediation$1.NetlistGenerationFailureCode = NetlistGenerationFailureCode = {}));
	class NetlistGenerationRemediation {
	    static { this.UPLOAD_BY_HAND = 'Meanwhile, the build can proceed with a netlist exported from the CAD tool (or supplied by the customer), uploaded from the netlist column on pipeline ops.'; }
	    static { this.ESCALATE = 'Escalate to dev with the build id and the error text below.'; }
	    static { this.UNNAMED = {
	        owner: RemediationOwner.Dev,
	        summary: 'Netlist generation failed in a way the generator did not name.',
	        steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	    }; }
	    static { this.BY_CODE = {
	        [NetlistGenerationFailureCode.DuplicateStagePath]: {
	            owner: RemediationOwner.Ops,
	            summary: 'Two of the uploaded files land on the same path, so the generator refused to overwrite either.',
	            steps: [
	                'Check the build files for duplicates (the same file uploaded twice, or two folders flattened together) and re-upload a clean set.',
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.UnsafePath]: {
	            owner: RemediationOwner.Ops,
	            summary: 'An uploaded file has a path that points outside the build, so the generator refused to stage it.',
	            steps: [
	                'Re-upload the files without absolute paths or ".." segments in their names.',
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.NoBuildFiles]: {
	            owner: RemediationOwner.Ops,
	            summary: 'The generator found no CAD files on the build to work from.',
	            steps: [
	                'Check that the upload contains the schematic and project files, and re-upload them if not.',
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.AltiumProjectShape]: {
	            owner: RemediationOwner.Customer,
	            summary: 'The upload has no Altium project file, or more than one, so the generator cannot tell which design to use.',
	            steps: [
	                'Ask the customer for an upload containing exactly one .PrjPcb and its schematics.',
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.ErcErrors]: {
	            owner: RemediationOwner.Customer,
	            summary: 'The CAD tool refused to export a netlist because the design fails its electrical rules check.',
	            steps: [
	                'Share the ERC errors below with the customer and ask for corrected files or a netlist they exported themselves.',
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.AltiumEmptyNetlist]: {
	            owner: RemediationOwner.Customer,
	            summary: 'Altium exported an empty netlist: schematic documents the project file lists are not in the upload (the error names them), or the design compiles to nothing.',
	            steps: [
	                'Ask the customer for the documents named in the error (or the whole project folder, zipped, so nothing is left out), or for a netlist they exported themselves.',
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.AltiumPartialNetlist]: {
	            owner: RemediationOwner.Dev,
	            summary: 'Altium kept exporting a netlist that leaves out a whole schematic sheet, or far fewer components than the schematics carry, so it was not delivered.',
	            steps: [
	                'The error text names the sheets and components left out on the last attempt; the triage bundle holds the ERC report and tool log of that attempt.',
	                NetlistGenerationRemediation.ESCALATE,
	                NetlistGenerationRemediation.UPLOAD_BY_HAND,
	            ],
	        },
	        [NetlistGenerationFailureCode.NonNativeNetlist]: {
	            owner: RemediationOwner.Dev,
	            summary: "OrCAD fell back to its built-in netlist emitter, so the generated netlist can't be trusted as a reference.",
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.GuestUnavailable]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The machine that runs the EDA tools was unavailable, and the retries ran out.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.BridgeDead]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The connection to the EDA tools stopped responding, and the retries ran out.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.CommandWedged]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The EDA tool stopped responding part-way through the export, and the retries ran out.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.NoSession]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The EDA tools had no session to run the export in, and the retries ran out.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.BudgetExhausted]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The export ran out of time on every attempt.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.AltiumGenerationFailed]: {
	            owner: RemediationOwner.Dev,
	            summary: 'Altium failed to compile the project, and the retries ran out.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.KicadScriptFailed]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The KiCad export script failed, and the retries ran out.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.AltiumUnsupportedBuild]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The installed Altium build cannot run the export.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.AltiumNotConfigured]: {
	            owner: RemediationOwner.Dev,
	            summary: 'Altium generation is not configured on the worker.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.NoToken]: {
	            owner: RemediationOwner.Dev,
	            summary: "The generator has no credentials for Cadstrom's API.",
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.NoGenerator]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The worker has no generator for this CAD tool, although generation was started for it.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.BadStubFile]: {
	            owner: RemediationOwner.Dev,
	            summary: "The worker's stub netlist is misconfigured.",
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.ApiFailed]: {
	            owner: RemediationOwner.Dev,
	            summary: "The generator could not read the build's files from Cadstrom's API.",
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	        [NetlistGenerationFailureCode.UploadFailed]: {
	            owner: RemediationOwner.Dev,
	            summary: 'The generator produced a netlist but could not upload it to the build.',
	            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
	        },
	    }; }
	    static { this.CODE_PREFIX = /^(NetlistGeneration_[A-Za-z]+):/; }
	    static codeOf(failure) {
	        if (failure.type !== undefined && NetlistGenerationRemediation.isCode(failure.type)) {
	            return failure.type;
	        }
	        const prefix = NetlistGenerationRemediation.CODE_PREFIX.exec(failure.message ?? '')?.[1];
	        return prefix !== undefined && NetlistGenerationRemediation.isCode(prefix) ? prefix : undefined;
	    }
	    static forCode(code) {
	        return code === undefined ? NetlistGenerationRemediation.UNNAMED : NetlistGenerationRemediation.BY_CODE[code];
	    }
	    static isCode(value) {
	        return Object.values(NetlistGenerationFailureCode).includes(value);
	    }
	}
	remediation$1.NetlistGenerationRemediation = NetlistGenerationRemediation;
	var ProjectParseFailureCode;
	(function (ProjectParseFailureCode) {
	    ProjectParseFailureCode["AltiumProjectListsNoSheets"] = "ProjectParse_AltiumProjectListsNoSheets";
	    ProjectParseFailureCode["AltiumProjectFileMissing"] = "ProjectParse_AltiumProjectFileMissing";
	})(ProjectParseFailureCode || (remediation$1.ProjectParseFailureCode = ProjectParseFailureCode = {}));
	class ProjectParseRemediation {
	    static { this.UNNAMED = {
	        owner: RemediationOwner.Ops,
	        summary: "The build's project files could not be parsed into a project model.",
	        steps: [
	            'Check that the upload contains the complete design in a supported CAD format, and re-upload it if not.',
	            'If the files look right, escalate to dev with the build id and the error text.',
	        ],
	    }; }
	    static { this.BY_CODE = {
	        [ProjectParseFailureCode.AltiumProjectListsNoSheets]: {
	            owner: RemediationOwner.Customer,
	            summary: 'The Altium project file lists no schematic sheets, and the upload gives the parser nothing to use in their place.',
	            steps: [
	                'Ask the customer to open the project in Altium, add the schematics to it, save, and upload the saved .PrjPcb together with every schematic (and the .PrjPcbStructure file when the project is hierarchical).',
	            ],
	        },
	        [ProjectParseFailureCode.AltiumProjectFileMissing]: {
	            owner: RemediationOwner.Customer,
	            summary: 'The upload has no Altium project file and no readable schematic to use in its place.',
	            steps: ['Ask the customer for an upload containing the .PrjPcb project file and its schematics.'],
	        },
	    }; }
	    static { this.CODE_PREFIX = /^(ProjectParse_[A-Za-z]+):/; }
	    static codeOf(reason) {
	        const prefix = ProjectParseRemediation.CODE_PREFIX.exec(reason)?.[1];
	        return prefix !== undefined && ProjectParseRemediation.isCode(prefix) ? prefix : undefined;
	    }
	    static guidance(reason) {
	        const code = reason === undefined ? undefined : ProjectParseRemediation.codeOf(reason);
	        return code === undefined ? ProjectParseRemediation.UNNAMED : ProjectParseRemediation.BY_CODE[code];
	    }
	    static isCode(value) {
	        return Object.values(ProjectParseFailureCode).includes(value);
	    }
	}
	remediation$1.ProjectParseRemediation = ProjectParseRemediation;
	
	return remediation$1;
}

var AltiumChannelNameOrder = {};

var hasRequiredAltiumChannelNameOrder;

function requireAltiumChannelNameOrder () {
	if (hasRequiredAltiumChannelNameOrder) return AltiumChannelNameOrder;
	hasRequiredAltiumChannelNameOrder = 1;
	Object.defineProperty(AltiumChannelNameOrder, "__esModule", { value: true });
	AltiumChannelNameOrder.compareAltiumChannelNames = void 0;
	const tokenizeAltiumChannelName = (name) => name.match(/\d+|[^\d]+/g) ?? [];
	const compareAltiumChannelNames = (aName, bName) => {
	    const aTokens = tokenizeAltiumChannelName(aName.toLowerCase());
	    const bTokens = tokenizeAltiumChannelName(bName.toLowerCase());
	    const sharedTokenCount = Math.min(aTokens.length, bTokens.length);
	    for (let index = 0; index < sharedTokenCount; index++) {
	        const aToken = aTokens[index];
	        const bToken = bTokens[index];
	        const aIsNumeric = /^\d+$/.test(aToken);
	        const bIsNumeric = /^\d+$/.test(bToken);
	        if (aIsNumeric && bIsNumeric) {
	            const numericOrder = Number(aToken) - Number(bToken);
	            if (numericOrder !== 0) {
	                return numericOrder;
	            }
	        }
	        else if (aIsNumeric !== bIsNumeric) {
	            return aIsNumeric ? -1 : 1;
	        }
	        else if (aToken !== bToken) {
	            return aToken < bToken ? -1 : 1;
	        }
	    }
	    return aTokens.length - bTokens.length;
	};
	AltiumChannelNameOrder.compareAltiumChannelNames = compareAltiumChannelNames;
	
	return AltiumChannelNameOrder;
}

var hasRequiredAltiumHierarchicalSchematicProcessor;

function requireAltiumHierarchicalSchematicProcessor () {
	if (hasRequiredAltiumHierarchicalSchematicProcessor) return AltiumHierarchicalSchematicProcessor;
	hasRequiredAltiumHierarchicalSchematicProcessor = 1;
	Object.defineProperty(AltiumHierarchicalSchematicProcessor, "__esModule", { value: true });
	AltiumHierarchicalSchematicProcessor.AltiumHierarchicalSchematicProcessor = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const AltiumSchematicTransformer_1 = requireAltiumSchematicTransformer();
	const SchematicInstanceTracker_1 = requireSchematicInstanceTracker();
	const AltiumNetNamingStrategy_1 = requireAltiumNetNamingStrategy();
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const altiumValidation_1 = requireAltiumValidation();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	const AltiumSheetSymbol_1 = requireAltiumSheetSymbol();
	const layers_1 = requireLayers();
	const AltiumNameGenerator_1 = requireAltiumNameGenerator();
	const AltiumFontMapper_1 = requireAltiumFontMapper();
	const AltiumAnnotationFileUtil_1 = requireAltiumAnnotationFileUtil();
	const PathUtils_1 = requirePathUtils();
	const NetCombobulator_1 = requireNetCombobulator();
	const remediation_1 = requireRemediation$1();
	const AltiumCombobulatorConfig_1 = requireAltiumCombobulatorConfig();
	const AltiumChannelNameOrder_1 = requireAltiumChannelNameOrder();
	class AltiumHierarchicalSchematicProcessor$1 {
	    constructor(parsedDef, projectConfigUtil, options = {}) {
	        this.parsedDef = parsedDef;
	        this.projectConfigUtil = projectConfigUtil;
	        this.instances = new Map();
	        this.instanceMap = new Map();
	        this.schematicTransformers = new Map();
	        this.processingResults = [];
	        this.flatNamingContext = undefined;
	        this.usedSymbols = new WeakSet();
	        this.hierarchyInstanceTrackers = new Map();
	        this.physicalChannelIndexes = new Map();
	        this.rootHierarchies = this.resolveHierarchies();
	        this.hierarchyMode = this.resolveHierarchyMode();
	        if (this.projectConfigUtil.checkHierarchyModeRequiresProjectStructure(this.hierarchyMode) &&
	            !this.projectConfigUtil.hasPrjpcbStructureFile) {
	            throw new Error(`Hierarchy mode ${this.hierarchyMode} requires the project structure file to be present`);
	        }
	        const isGlobalMode = this.hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Global;
	        if (isGlobalMode) {
	            this.flatNamingContext = new sigma_engine_shared_1.NetRefCtx();
	        }
	        this.fontMapper = new AltiumFontMapper_1.AltiumFontMapper(parsedDef);
	        this.projectModel = {
	            type: 'altium',
	            layerSets: {},
	            designs: {},
	            components: {},
	            modules: [],
	            warnings: [],
	            processingResults: {
	                type: 'project-processing',
	                message: 'Project processed successfully',
	                innerResults: [],
	            },
	            fonts: {
	                fonts: Object.fromEntries(this.fontMapper.projectFonts),
	            },
	            globalNets: {},
	        };
	        this.annotationUtil = new AltiumAnnotationFileUtil_1.AltiumAnnotationFileUtil(this.parsedDef, this.projectConfigUtil);
	        for (const diagnostic of this.annotationUtil.annotationDiagnostics) {
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: diagnostic,
	            });
	        }
	        if (this.usesCompiledSheetNumbering()) {
	            for (const diagnostic of this.annotationUtil.sheetNumberDiagnostics) {
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: diagnostic,
	                });
	            }
	        }
	        this.nameGenerator = new AltiumNameGenerator_1.AltiumNameGenerator(this.projectModel, this.parsedDef, this.projectConfigUtil, this.annotationUtil, (result) => this.projectModel.processingResults.innerResults.push(result));
	        this.namingStrategy = new AltiumNetNamingStrategy_1.AltiumNetNamingStrategy(this.projectConfigUtil, this.nameGenerator, this.hierarchyMode, {
	            enableDebug: options.netNamingDebug,
	        });
	    }
	    getNetNamingDebug() {
	        return this.namingStrategy.getNetNamingDebug();
	    }
	    get hierarchyInstances() {
	        return Array.from(this.instanceMap.values());
	    }
	    getHierarchyInstanceTracker(sourceFile) {
	        let tracker = this.hierarchyInstanceTrackers.get(sourceFile);
	        if (!tracker) {
	            tracker = new SchematicInstanceTracker_1.SchematicInstanceTracker(sourceFile, this.projectConfigUtil);
	            this.hierarchyInstanceTrackers.set(sourceFile, tracker);
	        }
	        return tracker;
	    }
	    initializeInstances(schematicHierarchy, parentInstance, fileDesignatorCounts, _projectConfigUtil, physicalChannelIndexes) {
	        const REPEAT_REGEX = /Repeat\(\s*([^,]+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i;
	        if (!parentInstance) {
	            const roomName = sigma_engine_shared_1.Files.basenamePath(schematicHierarchy.name.replace(/\.SchDoc$/i, ''));
	            return [
	                {
	                    instanceId: schematicHierarchy.name,
	                    hierarchyEntry: schematicHierarchy,
	                    path: [],
	                    pathUniqueId: '',
	                    sheetSymbol: undefined,
	                    numInstances: 1,
	                    totalSheetInstances: 1,
	                    instanceIndex: 0,
	                    channelIndex: 0,
	                    isMultiChannel: false,
	                    roomName: roomName,
	                    channelPrefix: roomName,
	                    physicalRoomName: roomName,
	                    physicalChannelPrefix: roomName,
	                    physicalChannelIndex: 0,
	                    children: [],
	                },
	            ];
	        }
	        const sortedAllSheetSymbols = parentInstance.hierarchyEntry.schematic
	            .getSheetSymbols()
	            .map((sheetSymbol, sourceOrder) => ({ sheetSymbol, sourceOrder }))
	            .sort((a, b) => {
	            const nameOrder = (0, AltiumChannelNameOrder_1.compareAltiumChannelNames)(a.sheetSymbol.getSheetName().text, b.sheetSymbol.getSheetName().text);
	            return nameOrder || b.sourceOrder - a.sourceOrder;
	        });
	        const sortedSheetSymbols = sortedAllSheetSymbols
	            .filter(({ sheetSymbol }) => {
	            return this.resolveSchematicKey(sheetSymbol.getFileNameText()) === schematicHierarchy.sourceFile;
	        })
	            .map(({ sheetSymbol }) => sheetSymbol);
	        return this.updateDesignatorCounts(fileDesignatorCounts, sortedSheetSymbols.flatMap((sheetSymbol) => {
	            const sheetName = sheetSymbol.getSheetNameText();
	            const repeatMatch = sheetName.match(REPEAT_REGEX);
	            if (repeatMatch) {
	                const channelPrefix = repeatMatch[1];
	                const channelIndex1 = parseInt(repeatMatch[2]);
	                const lastChannelIndexN = parseInt(repeatMatch[3]);
	                if (typeof channelIndex1 !== 'number' ||
	                    typeof lastChannelIndexN !== 'number' ||
	                    Number.isNaN(channelIndex1) ||
	                    Number.isNaN(lastChannelIndexN)) {
	                    throw new Error(`Invalid channel index in sheet name ${sheetName} - ${repeatMatch[2]} or ${repeatMatch[3]} is not a number`);
	                }
	                if (channelIndex1 < 0 || lastChannelIndexN < 0) {
	                    throw new Error(`Invalid channel index in sheet name ${sheetName} - ${repeatMatch[2]} or ${repeatMatch[3]} is negative`);
	                }
	                if (channelIndex1 > lastChannelIndexN) {
	                    throw new Error(`Invalid channel index in sheet name ${sheetName} - ${repeatMatch[2]} is greater than ${repeatMatch[3]}`);
	                }
	                const instances = [];
	                for (let i = channelIndex1; i <= lastChannelIndexN; i++) {
	                    const physicalChannelIndex = (physicalChannelIndexes.get(schematicHierarchy.sourceFile) ?? 0) + 1;
	                    physicalChannelIndexes.set(schematicHierarchy.sourceFile, physicalChannelIndex);
	                    const uniqueIdNo = schematicHierarchy.instanceTracker.claimNextSheetIndexInPath(parentInstance, schematicHierarchy, i);
	                    const uniqueId = `${uniqueIdNo}${sheetSymbol.uniqueId}`;
	                    const pathUniqueId = (0, PathUtils_1.joinPathUniqueIds)(parentInstance.pathUniqueId, uniqueId);
	                    instances.push({
	                        instanceId: `${schematicHierarchy.name}:${pathUniqueId}`,
	                        hierarchyEntry: schematicHierarchy,
	                        path: [...parentInstance.path, parentInstance],
	                        pathUniqueId,
	                        sheetSymbol,
	                        numInstances: lastChannelIndexN - channelIndex1 + 1,
	                        totalSheetInstances: 0,
	                        instanceIndex: i - channelIndex1,
	                        isMultiChannel: true,
	                        channelPrefix,
	                        channelIndex: i,
	                        roomChannelIndex: i,
	                        roomAlphaIndex: i,
	                        roomName: channelPrefix,
	                        physicalRoomName: channelPrefix,
	                        physicalChannelPrefix: channelPrefix,
	                        physicalChannelIndex,
	                        children: [],
	                    });
	                }
	                return instances;
	            }
	            const channelPrefix = sheetSymbol.getSheetNameText();
	            const pathUniqueId = (0, PathUtils_1.joinPathUniqueIds)(parentInstance.pathUniqueId, sheetSymbol.uniqueId);
	            const plainPhysicalChannelIndex = (physicalChannelIndexes.get(schematicHierarchy.sourceFile) ?? 0) + 1;
	            physicalChannelIndexes.set(schematicHierarchy.sourceFile, plainPhysicalChannelIndex);
	            return [
	                {
	                    instanceId: `${schematicHierarchy.name}:${pathUniqueId}`,
	                    hierarchyEntry: schematicHierarchy,
	                    path: [...parentInstance.path, parentInstance],
	                    pathUniqueId,
	                    sheetSymbol,
	                    numInstances: 1,
	                    totalSheetInstances: 0,
	                    instanceIndex: 0,
	                    channelIndex: 1,
	                    roomAlphaIndex: undefined,
	                    isMultiChannel: false,
	                    channelPrefix,
	                    roomName: channelPrefix,
	                    physicalRoomName: channelPrefix,
	                    physicalChannelPrefix: channelPrefix,
	                    physicalChannelIndex: plainPhysicalChannelIndex,
	                    children: [],
	                },
	            ];
	        }));
	    }
	    updateDesignatorCounts(fileDesignatorCounts, instances) {
	        const totalNumInstances = instances.reduce((p, c) => p + c.numInstances, 0);
	        for (const instance of instances) {
	            let designatorCount = fileDesignatorCounts.get(instance.channelPrefix);
	            if (!designatorCount) {
	                designatorCount = 0;
	            }
	            fileDesignatorCounts.set(instance.channelPrefix, ++designatorCount);
	            instance.totalSheetInstances = totalNumInstances;
	            const lastParent = instance.path[instance.path.length - 1];
	            if (lastParent) {
	                lastParent.children.push(instance);
	            }
	        }
	        return instances;
	    }
	    schematicFileKey(fileName) {
	        const base = sigma_engine_shared_1.Files.basenamePath(fileName.replace(/\\/g, '/'));
	        return (0, AltiumSheetSymbol_1.withDefaultSchDocExtension)(base).toLowerCase();
	    }
	    resolveSchematicKey(fileName) {
	        const normalizedFileName = fileName.replace(/\\/g, '/');
	        const exactKeys = Object.keys(this.parsedDef.schematics).filter((key) => key.replace(/\\/g, '/') === normalizedFileName);
	        if (exactKeys.length === 1) {
	            return exactKeys[0];
	        }
	        const resolution = this.projectConfigUtil.resolveProjectDocumentPath(fileName);
	        if (resolution.documentPath) {
	            const projectDocumentPath = resolution.documentPath;
	            const projectMatches = Object.keys(this.parsedDef.schematics).filter((key) => this.projectConfigUtil.resolveProjectDocumentPath(key).documentPath === projectDocumentPath);
	            if (projectMatches.length === 1) {
	                return projectMatches[0];
	            }
	            if (projectMatches.length > 1) {
	                throw new Error(`Ambiguous schematic reference "${fileName}": matches ${projectMatches.join(', ')}`);
	            }
	        }
	        const target = this.schematicFileKey(fileName);
	        const matches = Object.keys(this.parsedDef.schematics).filter((key) => this.schematicFileKey(key) === target);
	        if (matches.length > 1) {
	            throw new Error(`Ambiguous schematic reference "${fileName}": matches ${matches.join(', ')}`);
	        }
	        return matches[0];
	    }
	    resolveProjectDocumentPath(fileKey, reportZeroMatch = false) {
	        const reportedKeys = this.reportedProjectDocumentPathKeys ?? new Set();
	        this.reportedProjectDocumentPathKeys = reportedKeys;
	        const resolution = this.projectConfigUtil.resolveProjectDocumentPath(fileKey);
	        if (reportZeroMatch && resolution.documentPath === undefined) {
	            if (!reportedKeys.has(fileKey)) {
	                reportedKeys.add(fileKey);
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    message: `No project document path matches schematic key ${fileKey}`,
	                });
	            }
	        }
	        return resolution.documentPath;
	    }
	    resolveHierarchies() {
	        const schematicHighestHierarchyIndexes = new Map();
	        const builtHierarchies = new Map();
	        const roots = this.resolveRootDocumentFileNames().flatMap((rawFileName) => {
	            const skipRoot = (message) => {
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message,
	                });
	                return [];
	            };
	            const fileName = this.resolveSchematicKey(rawFileName) ?? rawFileName;
	            const schematicDef = this.parsedDef.schematics[fileName];
	            if (!schematicDef) {
	                return skipRoot(`Root schematic ${rawFileName} not found — skipping the missing document`);
	            }
	            if (!schematicDef.validationResult?.isValid) {
	                return skipRoot(`Root schematic ${fileName} is not valid — skipping the document`);
	            }
	            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
	            if (!schematic) {
	                return skipRoot(`Parsed root schematic ${fileName} is not available — skipping the document`);
	            }
	            let schematicIndex = 0;
	            let sheetNumber;
	            try {
	                sheetNumber = this.projectConfigUtil.getAltiumSchematicSheetNumber(fileName);
	                if (!Number.isInteger(sheetNumber) || sheetNumber < 1) {
	                    throw new Error(`Invalid sheet number for ${fileName}`);
	                }
	                schematicIndex = sheetNumber - 1;
	            }
	            catch (e) {
	                sheetNumber = undefined;
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: `Failed to get Altium schematic index for ${fileName}: ${e}`,
	                });
	            }
	            const highestHierarchyIndex = schematicHighestHierarchyIndexes.get(fileName) ?? 0;
	            schematicHighestHierarchyIndexes.set(fileName, highestHierarchyIndex);
	            return this.buildChildHierarchies({
	                name: fileName.replace(/\.SchDoc$/i, ''),
	                sourceFile: fileName,
	                projectDocumentPath: this.resolveProjectDocumentPath(fileName, true),
	                schematicIndex,
	                sheetNumber,
	                children: [],
	                parents: [],
	                schematic,
	                instanceTracker: this.getHierarchyInstanceTracker(fileName),
	                hierarchyIndex: highestHierarchyIndex,
	            }, schematicHighestHierarchyIndexes, builtHierarchies);
	        });
	        if (roots.length === 0) {
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: 'No root hierarchies found',
	            });
	        }
	        else if (roots.length > 1 &&
	            (this.hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || this.hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical)) {
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: 'Invalid Altium project: Multiple root hierarchies found',
	            });
	        }
	        return roots;
	    }
	    resolveRootDocumentFileNames() {
	        const configuredHierarchyMode = this.projectConfigUtil.getDesignConfiguration('HierarchyMode') ?? altiumProjectFileTypes_1.HierarchyMode.Automatic;
	        if (configuredHierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical ||
	            configuredHierarchyMode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
	            if (this.projectConfigUtil.listsNoSchematics) {
	                throw new Error(`${this.noSheetsFailureCode()}: Hierarchy mode ${configuredHierarchyMode} needs the project to name its top sheet, but the project file lists no schematic sheets and there is no project structure file`);
	            }
	            return this.projectConfigUtil.getTopLevelDocuments('.SchDoc');
	        }
	        const topLevelDocuments = this.resolveTopLevelDocuments();
	        if (this.projectConfigUtil.listsNoSchematics) {
	            return topLevelDocuments;
	        }
	        if (configuredHierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Automatic &&
	            this.topLevelDocumentsContainSheetEntries(topLevelDocuments)) {
	            return topLevelDocuments;
	        }
	        return this.projectConfigUtil.getFlatProjectDocuments('.SchDoc');
	    }
	    resolveTopLevelDocuments() {
	        if (!this.projectConfigUtil.listsNoSchematics) {
	            return this.projectConfigUtil.getTopLevelDocuments('.SchDoc');
	        }
	        if (this.fallbackRootDocuments === undefined) {
	            const roots = this.resolveUploadedRootDocuments();
	            const projectFileName = this.projectConfigUtil.projectFileName;
	            const listing = projectFileName ?
	                `Project file ${projectFileName} lists no schematic sheets and there is no project structure file`
	                : 'No Altium project file was uploaded';
	            if (roots.length === 0) {
	                throw new Error(`${this.noSheetsFailureCode()}: ${listing}, and no schematic was uploaded to use instead`);
	            }
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                message: `${listing}; using the uploaded schematics as flat top-level sheets: ${roots.join(', ')}`,
	            });
	            this.fallbackRootDocuments = roots;
	        }
	        return this.fallbackRootDocuments;
	    }
	    resolveUploadedRootDocuments() {
	        const uploadedKeys = Object.keys(this.parsedDef.schematics)
	            .filter((key) => key.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.SchDoc.toLowerCase()))
	            .sort();
	        const referencedKeys = new Set();
	        for (const key of uploadedKeys) {
	            const schematicDef = this.parsedDef.schematics[key];
	            if (!schematicDef?.validationResult?.isValid) {
	                continue;
	            }
	            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
	            for (const sheetSymbol of schematic?.getSheetSymbols() ?? []) {
	                const reference = (0, AltiumSheetSymbol_1.withDefaultSchDocExtension)(sheetSymbol.getFileNameText());
	                const referencedKey = reference ? this.resolveSchematicKey(reference) : undefined;
	                if (referencedKey !== undefined && referencedKey !== key) {
	                    referencedKeys.add(referencedKey);
	                }
	            }
	        }
	        return uploadedKeys.filter((key) => !referencedKeys.has(key));
	    }
	    noSheetsFailureCode() {
	        return this.projectConfigUtil.projectFileName ?
	            remediation_1.ProjectParseFailureCode.AltiumProjectListsNoSheets
	            : remediation_1.ProjectParseFailureCode.AltiumProjectFileMissing;
	    }
	    topLevelDocumentsContainSheetEntries(fileNames) {
	        const pending = [...fileNames];
	        const visited = new Set();
	        for (const fileName of pending) {
	            const key = this.resolveSchematicKey(fileName) ?? fileName;
	            if (visited.has(key)) {
	                continue;
	            }
	            visited.add(key);
	            const schematicDef = this.parsedDef.schematics[key];
	            if (!schematicDef?.validationResult?.isValid) {
	                continue;
	            }
	            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
	            for (const sheetSymbol of schematic?.getSheetSymbols() ?? []) {
	                if (sheetSymbol.getEntries().length > 0) {
	                    return true;
	                }
	                pending.push(sheetSymbol.getFileNameText());
	            }
	        }
	        return false;
	    }
	    buildChildHierarchies(parentHierarchy, schematicHighestHierarchyIndexes, builtHierarchies) {
	        builtHierarchies.set(parentHierarchy.sourceFile, parentHierarchy);
	        for (const sheet of parentHierarchy.schematic.getSheetSymbols()) {
	            const referencedFileName = sheet.getFileNameText();
	            const fileName = this.resolveSchematicKey(referencedFileName) ?? referencedFileName;
	            const currentHierarchyIndex = parentHierarchy.hierarchyIndex + 1;
	            let highestHierarchyIndex = schematicHighestHierarchyIndexes.get(fileName) ?? currentHierarchyIndex;
	            if (highestHierarchyIndex > currentHierarchyIndex) {
	                highestHierarchyIndex = currentHierarchyIndex;
	            }
	            schematicHighestHierarchyIndexes.set(fileName, highestHierarchyIndex);
	            const builtHierarchy = builtHierarchies.get(fileName);
	            if (builtHierarchy) {
	                if (builtHierarchy.hierarchyIndex > currentHierarchyIndex) {
	                    builtHierarchy.hierarchyIndex = currentHierarchyIndex;
	                }
	                if (!parentHierarchy.children.some((c) => c.sourceFile === fileName)) {
	                    parentHierarchy.children.push(builtHierarchies.get(fileName));
	                }
	                continue;
	            }
	            const schematicDef = this.parsedDef.schematics[fileName];
	            if (!schematicDef) {
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: `Child schematic ${referencedFileName} not found — skipping the missing document`,
	                });
	                continue;
	            }
	            if (!schematicDef.validationResult?.isValid) {
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: `Child schematic ${fileName} is not valid — skipping the document`,
	                });
	                continue;
	            }
	            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
	            if (!schematic) {
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: `Parsed child schematic ${fileName} is not available — skipping the document`,
	                });
	                continue;
	            }
	            let schematicIndex = 0;
	            let sheetNumber;
	            try {
	                sheetNumber = this.projectConfigUtil.getAltiumSchematicSheetNumber(fileName);
	                if (!Number.isInteger(sheetNumber) || sheetNumber < 1) {
	                    throw new Error(`Invalid sheet number for ${fileName}`);
	                }
	                schematicIndex = sheetNumber - 1;
	            }
	            catch (e) {
	                sheetNumber = undefined;
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    message: `Failed to get Altium schematic index for ${fileName}: ${e}`,
	                });
	            }
	            const childHierarchy = this.buildChildHierarchies({
	                name: fileName.replace(/\.SchDoc$/i, ''),
	                sourceFile: fileName,
	                projectDocumentPath: this.resolveProjectDocumentPath(fileName, true),
	                schematicIndex,
	                sheetNumber,
	                children: [],
	                parents: [parentHierarchy],
	                schematic,
	                instanceTracker: this.getHierarchyInstanceTracker(fileName),
	                hierarchyIndex: highestHierarchyIndex,
	            }, schematicHighestHierarchyIndexes, builtHierarchies);
	            parentHierarchy.children.push(childHierarchy);
	        }
	        return parentHierarchy;
	    }
	    resolveHierarchyMode() {
	        let hierarchyMode = this.projectConfigUtil.getDesignConfiguration('HierarchyMode');
	        if (hierarchyMode === undefined) {
	            hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Automatic;
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                message: 'HierarchyMode is not set in the project config, using Automatic',
	            });
	        }
	        if (hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Automatic) {
	            const rootHierarchy = this.rootHierarchies[0];
	            if (!rootHierarchy) {
	                return altiumProjectFileTypes_1.HierarchyMode.Flat;
	            }
	            if (this.projectConfigUtil.listsNoSchematics) {
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                    message: 'HierarchyMode is Automatic, using Flat because the project names no schematic sheet to start from',
	                });
	                return altiumProjectFileTypes_1.HierarchyMode.Flat;
	            }
	            const topLevelDocuments = this.resolveTopLevelDocuments();
	            if (this.topLevelDocumentsContainSheetEntries(topLevelDocuments)) {
	                hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Hierarchical;
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                    message: 'HierarchyMode is Automatic, using Hierarchical because there are sheet entries on the top sheet',
	                });
	                return hierarchyMode;
	            }
	            const ports = rootHierarchy.schematic.getPorts();
	            if (ports.length > 0) {
	                hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Flat;
	                this.processingResults.push({
	                    type: 'hierarchical-schematic-processing',
	                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                    message: 'HierarchyMode is Automatic, using Flat because there are ports on the top sheet',
	                });
	                return hierarchyMode;
	            }
	            hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Global;
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                message: 'HierarchyMode is Automatic, using Global because there are no sheet entries or ports on the top sheet',
	            });
	        }
	        return hierarchyMode;
	    }
	    buildInstanceMap() {
	        this.physicalChannelIndexes.clear();
	        const traversals = this.rootHierarchies.map((hierarchy) => this.traverseHierarchy(hierarchy, []));
	        const sourceInstanceCounts = new Map();
	        for (const traversal of traversals) {
	            for (const [sourceFile, instances] of traversal.schematicInstances) {
	                sourceInstanceCounts.set(sourceFile, (sourceInstanceCounts.get(sourceFile) ?? 0) + instances.length);
	            }
	        }
	        const designWideTotals = this.computeDesignWideInstanceTotals(AltiumHierarchicalSchematicProcessor$1.mergeFileDesignatorCounts(traversals.map((traversal) => traversal.fileDesignatorCounts)));
	        for (const traversal of traversals) {
	            for (const [sourceFile, instances] of traversal.schematicInstances) {
	                for (const instance of instances) {
	                    this.resolveCompiledSheetNumber(instance, sourceInstanceCounts.get(sourceFile) ?? 0);
	                }
	            }
	            this.setChannelIndexes(traversal.rootInstance, traversal.parentHierarchyMap, traversal.fileDesignatorCounts, designWideTotals);
	            this.createTransformers(traversal.schematicInstances);
	        }
	        for (const transformer of this.schematicTransformers.values()) {
	            transformer.prepareModuleAnnotationClaims();
	        }
	        for (const transformer of this.schematicTransformers.values()) {
	            transformer.initializeModuleNames();
	        }
	        for (const transformer of this.schematicTransformers.values()) {
	            transformer.initializeSchematicModel();
	        }
	    }
	    static mergeFileDesignatorCounts(perRootCounts) {
	        const merged = new Map();
	        for (const rootCounts of perRootCounts) {
	            for (const [sourceFile, designatorCounts] of rootCounts) {
	                const mergedDesignatorCounts = merged.get(sourceFile) ?? new Map();
	                merged.set(sourceFile, mergedDesignatorCounts);
	                for (const [channelPrefix, count] of designatorCounts) {
	                    mergedDesignatorCounts.set(channelPrefix, (mergedDesignatorCounts.get(channelPrefix) ?? 0) + count);
	                }
	            }
	        }
	        return merged;
	    }
	    traverseHierarchy(hierarchy, path) {
	        const schematicInstances = new Map();
	        const fileDesignatorCounts = new Map();
	        const queue = [];
	        queue.push({
	            hierarchy,
	            path,
	            parentInstance: path[path.length - 1],
	        });
	        let rootInstance = undefined;
	        const parentHierarchyMap = new Map();
	        while (queue.length > 0) {
	            const current = queue.shift();
	            let designatorCounts = fileDesignatorCounts.get(current.hierarchy.sourceFile);
	            if (!designatorCounts) {
	                designatorCounts = new Map();
	                fileDesignatorCounts.set(current.hierarchy.sourceFile, designatorCounts);
	            }
	            const instances = this.initializeInstances(current.hierarchy, current.parentInstance, designatorCounts, this.projectConfigUtil, this.physicalChannelIndexes);
	            const sourceFile = current.hierarchy.sourceFile;
	            if (!schematicInstances.has(sourceFile)) {
	                schematicInstances.set(sourceFile, []);
	            }
	            schematicInstances.get(sourceFile).push(...instances);
	            for (const instance of instances) {
	                if (!rootInstance) {
	                    if (instances.length > 1) {
	                        throw new Error('Multiple root instances found');
	                    }
	                    rootInstance = instance;
	                }
	                else {
	                    if (!current.parentInstance) {
	                        throw new Error('No parent instance found');
	                    }
	                    if (!parentHierarchyMap.has(current.parentInstance)) {
	                        parentHierarchyMap.set(current.parentInstance, []);
	                    }
	                    parentHierarchyMap.get(current.parentInstance).push(instance);
	                }
	                this.instanceMap.set(instance.instanceId, instance);
	                for (let i = 0; i < current.hierarchy.children.length; i++) {
	                    const child = current.hierarchy.children[i];
	                    queue.push({
	                        hierarchy: child,
	                        path: [...current.path, instance],
	                        parentInstance: instance,
	                    });
	                }
	            }
	        }
	        if (!rootInstance) {
	            throw new Error('No root instance found');
	        }
	        return { rootInstance, parentHierarchyMap, schematicInstances, fileDesignatorCounts };
	    }
	    resolveCompiledSheetNumber(instance, sourceInstanceCount) {
	        if (!this.usesCompiledSheetNumbering()) {
	            return;
	        }
	        const resolution = this.annotationUtil.getCompiledSheetNumber(instance.pathUniqueId, instance.hierarchyEntry.projectDocumentPath ?? instance.hierarchyEntry.sourceFile);
	        if (resolution.sheetNumber !== undefined) {
	            instance.compiledSheetNumber = resolution.sheetNumber;
	            return;
	        }
	        if (resolution.hasCompiledSheetNumberData) {
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: resolution.diagnostic ??
	                    `Could not resolve a unique compiled sheet-number record for ${instance.hierarchyEntry.sourceFile}`,
	            });
	            return;
	        }
	        const sourceFile = instance.hierarchyEntry.sourceFile;
	        try {
	            if (sourceInstanceCount !== 1) {
	                throw new Error(`Structural sheet number for ${sourceFile} requires one hierarchy placement; found ${sourceInstanceCount}`);
	            }
	            const structuralSheetNumber = this.projectConfigUtil.getUnambiguousAltiumSchematicSheetNumber(sourceFile);
	            if (!Number.isInteger(structuralSheetNumber) || structuralSheetNumber < 1) {
	                throw new Error(`Invalid sheet number for ${sourceFile}`);
	            }
	            instance.compiledSheetNumber = structuralSheetNumber;
	        }
	        catch (error) {
	            const structuralDiagnostic = error instanceof Error ? error.message : String(error);
	            this.processingResults.push({
	                type: 'hierarchical-schematic-processing',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                message: structuralDiagnostic,
	            });
	        }
	    }
	    usesCompiledSheetNumbering() {
	        return (this.projectConfigUtil.getDesignConfigurationBoolean('AppendSheetNumberToLocalNets') &&
	            this.projectConfigUtil.getDesignConfigurationBoolean('AutoSheetNumbering') &&
	            this.projectConfigUtil.getDesignConfigurationBoolean('ReorderDocumentsOnCompile'));
	    }
	    createTransformers(schematicInstances) {
	        const rootSourceFiles = new Set(this.rootHierarchies.map((hierarchy) => hierarchy.sourceFile));
	        for (const [sourceFile, instances] of schematicInstances) {
	            const isOwnRootTraversal = instances.some((instance) => instance.path.length === 0);
	            if (this.schematicTransformers.has(sourceFile) && rootSourceFiles.has(sourceFile) && !isOwnRootTraversal) {
	                continue;
	            }
	            const hierarchyEntry = instances[0].hierarchyEntry;
	            const instanceCombobulators = new Map();
	            for (const instance of instances) {
	                instanceCombobulators.set(instance, this.createInstanceCombobulator(instance.instanceId));
	            }
	            const existingTransformer = rootSourceFiles.has(sourceFile) ? undefined : this.schematicTransformers.get(sourceFile);
	            if (existingTransformer) {
	                existingTransformer.addInstances(instances, instanceCombobulators);
	                continue;
	            }
	            const transformer = new AltiumSchematicTransformer_1.AltiumSchematicTransformer(hierarchyEntry.schematic, this.parsedDef, this.projectConfigUtil, this.nameGenerator, instances, this.projectModel, instanceCombobulators, this.fontMapper, this.namingStrategy, this.usedSymbols);
	            this.schematicTransformers.set(sourceFile, transformer);
	        }
	    }
	    resolveChannelRoomName(instance) {
	        const style = this.projectConfigUtil.getDesignConfiguration('ChannelRoomNamingStyle') ??
	            altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatNumericWithNames;
	        const parentIsMultiChannel = instance.path.some((ancestor) => ancestor.isMultiChannel || ancestor.numInstances > 1);
	        const localChannelIndex = instance.roomChannelIndex ?? instance.channelIndex;
	        const localAlphaIndex = instance.roomAlphaIndex ?? instance.instanceIndex + 1;
	        const alphaIndex = style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatAlphaWithNames && parentIsMultiChannel ?
	            (instance.physicalChannelIndex ?? localAlphaIndex + 1) - 1
	            : localAlphaIndex;
	        const usesAlpha = style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatAlphaWithNames ||
	            style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.AlphaNamePath ||
	            (style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.MixedNamePath && !parentIsMultiChannel);
	        const localRoomName = usesAlpha ?
	            `${instance.channelPrefix}${(0, AltiumNameGenerator_1.channelAlphaSuffix)(alphaIndex + 1)}`
	            : `${instance.channelPrefix}${style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatNumericWithNames ? instance.channelIndex : localChannelIndex}`;
	        const usesPath = style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.NumericNamePath ||
	            style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.AlphaNamePath ||
	            style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.MixedNamePath;
	        const parent = instance.path.at(-1);
	        if (usesPath && parent && (parent.isMultiChannel || parent.numInstances > 1)) {
	            const separator = this.projectConfigUtil.getDesignConfiguration('ChannelRoomLevelSeperator');
	            return `${parent.roomName}${separator}${localRoomName}`;
	        }
	        return localRoomName;
	    }
	    setChannelIndexes(parentInstance, schematicInstances, fileDesignatorCounts, designWideTotals = this.computeDesignWideInstanceTotals(fileDesignatorCounts)) {
	        if (parentInstance.numInstances > 1) {
	            if (!parentInstance.channelPrefix) {
	                throw new Error(`Invariant failed: Expected channel prefix for instance ${parentInstance.pathUniqueId}`);
	            }
	            const channelName = this.annotationUtil.getChannelNameByUniqueId(parentInstance.pathUniqueId);
	            if (!parentInstance.isMultiChannel && channelName) {
	                if (channelName) {
	                    if (!channelName.startsWith(parentInstance.channelPrefix)) {
	                        throw new Error(`Channel name ${channelName} does not start with channel prefix ${parentInstance.channelPrefix}`);
	                    }
	                    const channelIndexStr = channelName.substring(parentInstance.channelPrefix.length);
	                    if (!/^\d+$/.test(channelIndexStr)) {
	                        throw new Error(`Channel index ${channelIndexStr} is not a number`);
	                    }
	                    parentInstance.channelIndex = parseInt(channelIndexStr, 10);
	                    parentInstance.roomName = channelName;
	                    if (!parentInstance.inheritsPhysicalNamingContext) {
	                        parentInstance.physicalChannelIndex = this.getPhysicalSiblingChannelIndex(parentInstance);
	                        this.setPhysicalNamingContext(parentInstance, parentInstance);
	                    }
	                    parentInstance.hierarchyEntry.instanceTracker.setClaimedChannelIndex(parentInstance.channelPrefix, parentInstance.channelIndex);
	                }
	            }
	            else {
	                const channelIndex = parentInstance.isMultiChannel ?
	                    parentInstance.hierarchyEntry.instanceTracker.claimNextChannelIndex(parentInstance.channelPrefix, parentInstance.channelIndex)
	                    : parentInstance.hierarchyEntry.instanceTracker.claimPreferredOrLowestAvailableChannelIndex(parentInstance.channelPrefix, parentInstance.channelIndex);
	                parentInstance.channelIndex = channelIndex;
	                parentInstance.roomName = this.resolveChannelRoomName(parentInstance);
	                if (!parentInstance.inheritsPhysicalNamingContext) {
	                    this.setPhysicalNamingContext(parentInstance, parentInstance);
	                }
	            }
	        }
	        const instances = schematicInstances.get(parentInstance);
	        if (!instances) {
	            return;
	        }
	        const siblingPositionByFile = new Map();
	        for (const child of instances) {
	            const sheetName = child.sheetSymbol?.getSheetNameText();
	            if (sheetName !== undefined) {
	                const sourceFileCounts = fileDesignatorCounts.get(child.hierarchyEntry.sourceFile);
	                if (!sourceFileCounts) {
	                    throw new Error(`Invariant failed: Expected designator counts for ${child.hierarchyEntry.sourceFile}`);
	                }
	                const siblingPosition = (siblingPositionByFile.get(child.hierarchyEntry.sourceFile) ?? 0) + 1;
	                siblingPositionByFile.set(child.hierarchyEntry.sourceFile, siblingPosition);
	                child.numInstances = sourceFileCounts.get(child.channelPrefix);
	                child.designWideInstances = this.resolveDesignWideInstances(child, designWideTotals);
	                if (!child.isMultiChannel && (child.numInstances > 1 || child.totalSheetInstances > 1)) {
	                    const ordinal = child.physicalChannelIndex ?? siblingPosition;
	                    child.channelIndex = ordinal;
	                    child.roomChannelIndex = ordinal;
	                    child.physicalChannelIndex = ordinal;
	                    child.roomAlphaIndex = ordinal - 1;
	                }
	                if (this.shouldInheritPhysicalNamingContext(parentInstance, child, instances)) {
	                    this.setPhysicalNamingContext(child, parentInstance);
	                    child.physicalChannelIndex = this.getPhysicalSiblingChannelIndex(parentInstance);
	                    child.inheritsPhysicalNamingContext = true;
	                }
	                else {
	                    this.setPhysicalNamingContext(child, child);
	                    child.inheritsPhysicalNamingContext = false;
	                }
	            }
	        }
	        const sortedChildren = instances
	            .map((instance) => [
	            instance,
	            instance.pathUniqueId
	                .split('\\')
	                .map((id) => id.padStart(13, '0'))
	                .join('\\'),
	        ])
	            .sort(([instanceA, a], [instanceB, b]) => {
	            let instanceAPri = 0;
	            let instanceBPri = 0;
	            if (instanceA.numInstances > 1 &&
	                !instanceA.isMultiChannel &&
	                this.annotationUtil.getChannelNameByUniqueId(instanceA.pathUniqueId)) {
	                instanceAPri = 1;
	            }
	            if (instanceB.numInstances > 1 &&
	                !instanceB.isMultiChannel &&
	                this.annotationUtil.getChannelNameByUniqueId(instanceB.pathUniqueId)) {
	                instanceBPri = 1;
	            }
	            if (instanceAPri !== instanceBPri) {
	                return instanceBPri - instanceAPri;
	            }
	            return (0, AltiumChannelNameOrder_1.compareAltiumChannelNames)(a, b);
	        });
	        for (const [childInstance] of sortedChildren) {
	            this.setChannelIndexes(childInstance, schematicInstances, fileDesignatorCounts, designWideTotals);
	        }
	    }
	    computeDesignWideInstanceTotals(fileDesignatorCounts) {
	        const designWideTotals = new Map();
	        for (const [sourceFile, designatorCounts] of fileDesignatorCounts) {
	            let total = 0;
	            for (const count of designatorCounts.values()) {
	                total += count;
	            }
	            designWideTotals.set(sourceFile, total);
	        }
	        return designWideTotals;
	    }
	    resolveDesignWideInstances(instance, designWideTotals) {
	        const hasMultiInstanceAncestor = instance.path.some((ancestor) => ancestor.isMultiChannel || ancestor.numInstances > 1 || ancestor.totalSheetInstances > 1);
	        if (hasMultiInstanceAncestor) {
	            return undefined;
	        }
	        return designWideTotals.get(instance.hierarchyEntry.sourceFile);
	    }
	    shouldInheritPhysicalNamingContext(parentInstance, childInstance, siblingInstances) {
	        if (childInstance.totalSheetInstances !== 1 || childInstance.numInstances <= 1) {
	            return false;
	        }
	        if (parentInstance.isMultiChannel) {
	            return false;
	        }
	        const directSiblingsForSameSheet = siblingInstances.filter((instance) => instance.hierarchyEntry.sourceFile === childInstance.hierarchyEntry.sourceFile &&
	            instance.channelPrefix === childInstance.channelPrefix);
	        if (directSiblingsForSameSheet.length !== 1) {
	            return false;
	        }
	        return parentInstance.numInstances === 1 && parentInstance.totalSheetInstances > 1;
	    }
	    getPhysicalSiblingChannelIndex(instance) {
	        const parent = instance.path.at(-1);
	        if (!parent) {
	            return instance.physicalChannelIndex ?? instance.channelIndex;
	        }
	        const siblingInstances = parent.children.filter((sibling) => sibling.hierarchyEntry.sourceFile === instance.hierarchyEntry.sourceFile);
	        const siblingIndex = siblingInstances.indexOf(instance);
	        if (siblingIndex === -1) {
	            return instance.physicalChannelIndex ?? instance.channelIndex;
	        }
	        return siblingIndex + 1;
	    }
	    setPhysicalNamingContext(target, source) {
	        target.physicalRoomName = source.physicalRoomName ?? source.roomName;
	        target.physicalChannelPrefix = source.physicalChannelPrefix ?? source.channelPrefix;
	        target.physicalChannelIndex = source.physicalChannelIndex ?? source.channelIndex;
	    }
	    resolvePhysicalNetNames() {
	        const hierarchy = this.buildCombobulationHierarchy();
	        const physicalNetGroups = this.namingStrategy.resolvePhysicalNetNames(hierarchy);
	        const netGroupsMap = new Map();
	        for (let i = 0; i < physicalNetGroups.length; i++) {
	            netGroupsMap.set(`group_${i}`, physicalNetGroups[i]);
	        }
	        this.updateNetRefsToPhysicalNames(netGroupsMap);
	        this.physicalNetGroups = netGroupsMap;
	        return netGroupsMap;
	    }
	    buildCombobulationHierarchy() {
	        const rootInstances = Array.from(this.instanceMap.values()).filter((inst) => inst.path.length === 0);
	        if (rootInstances.length === 0) {
	            throw new Error('No root instances found in hierarchy');
	        }
	        const rootNodes = rootInstances.map((rootInstance) => {
	            const rootInfo = this.instances.get(rootInstance.instanceId);
	            if (!rootInfo) {
	                throw new Error(`No instance info found for root instance ${rootInstance.instanceId}`);
	            }
	            return this.buildCombobulationHierarchyNode(rootInstance, rootInfo);
	        });
	        if (rootNodes.length === 1) {
	            return rootNodes[0];
	        }
	        return this.namingStrategy.createVirtualRootHierarchy(rootNodes);
	    }
	    buildCombobulationHierarchyNode(instance, instanceInfo) {
	        const children = [];
	        for (const [childPathId, childInfo] of this.instances.entries()) {
	            const childInstance = this.instanceMap.get(childPathId);
	            if (!childInstance) {
	                continue;
	            }
	            if (childInstance.path.length === instance.path.length + 1 &&
	                childInstance.path[childInstance.path.length - 1] === instance) {
	                children.push(this.buildCombobulationHierarchyNode(childInstance, childInfo));
	            }
	        }
	        return {
	            pathUniqueId: instance.pathUniqueId,
	            hierarchyDepth: instance.path.length,
	            instance,
	            combobulator: instanceInfo.combobulator,
	            children,
	        };
	    }
	    getFinalModel() {
	        if (!this.physicalNetGroups) {
	            throw new Error('Physical net names not resolved');
	        }
	        const layerSets = new Set();
	        const finalizedTransformers = new Set();
	        for (const [_instance, transformer] of this.schematicTransformers) {
	            if (!finalizedTransformers.has(transformer)) {
	                transformer.finalize(Array.from(this.physicalNetGroups.values()));
	                finalizedTransformers.add(transformer);
	                layerSets.add(transformer.model.layerSet);
	            }
	        }
	        const layerSetsDefs = (0, layers_1.getAltiumDefaultLayerSets)();
	        for (const layerSet of layerSets) {
	            if (!this.projectModel.layerSets[layerSet]) {
	                if (!layerSetsDefs[layerSet]) {
	                    throw new Error(`Layer set ${layerSet} not found in default layer sets`);
	                }
	                this.projectModel.layerSets[layerSet] = layerSetsDefs[layerSet].layers;
	            }
	        }
	        const allExtractions = new Map();
	        for (const transformer of finalizedTransformers) {
	            for (const extraction of transformer.extractedImages) {
	                if (!allExtractions.has(extraction.ref)) {
	                    allExtractions.set(extraction.ref, {
	                        type: sigma_engine_shared_1.ProjectModelAssetType.Image,
	                        name: extraction.name,
	                        mimeType: extraction.mimeType,
	                    });
	                }
	            }
	        }
	        if (allExtractions.size > 0) {
	            this.projectModel.assets = Array.from(allExtractions.entries()).map(([ref, { type, name, mimeType }]) => ({
	                type,
	                ref,
	                name,
	                mimeType,
	            }));
	        }
	        const moduleNames = new Set(this.projectModel.modules.map((m) => m.name));
	        if (this.projectModel.modules.length !== moduleNames.size) {
	            throw new Error('Duplicate module names found in project model');
	        }
	        return this.projectModel;
	    }
	    getExtractedImages() {
	        const seen = new Map();
	        for (const transformer of this.schematicTransformers.values()) {
	            for (const extraction of transformer.extractedImages) {
	                if (!seen.has(extraction.ref)) {
	                    seen.set(extraction.ref, extraction);
	                }
	            }
	        }
	        return Array.from(seen.values());
	    }
	    buildDesignHierarchy() {
	        const roots = [];
	        for (const rootHierarchy of this.rootHierarchies) {
	            const rootInstance = this.hierarchyInstances.find((inst) => inst.hierarchyEntry === rootHierarchy && inst.path.length === 0);
	            if (!rootInstance) {
	                continue;
	            }
	            roots.push(this.buildHierarchyNode(rootInstance));
	        }
	        return roots;
	    }
	    buildHierarchyNode(instance) {
	        const transformer = this.schematicTransformers.get(instance.hierarchyEntry.sourceFile);
	        const designId = transformer?.model.designId ?? instance.hierarchyEntry.sourceFile;
	        const isMultiInstance = instance.totalSheetInstances > 1;
	        const instanceId = isMultiInstance ? instance.pathUniqueId : undefined;
	        let label = `[${instance.hierarchyEntry.schematicIndex + 1}] ${sigma_engine_shared_1.Files.basenamePath(instance.hierarchyEntry.sourceFile)}`;
	        if (isMultiInstance && instance.isMultiChannel && instance.roomName) {
	            label = instance.roomName;
	        }
	        const children = this.buildHierarchyChildren(instance.children.sort((a, b) => {
	            const sheetIndexDiff = a.hierarchyEntry.schematicIndex - b.hierarchyEntry.schematicIndex;
	            if (sheetIndexDiff !== 0) {
	                return sheetIndexDiff;
	            }
	            return a.channelIndex - b.channelIndex;
	        }));
	        return {
	            designId,
	            label,
	            ...(instanceId !== undefined && { instanceId }),
	            ...(children.length > 0 && { children }),
	        };
	    }
	    buildHierarchyChildren(children) {
	        const result = [];
	        const groupsBySourceFile = new Map();
	        const insertionOrder = [];
	        for (const child of children) {
	            const sourceFile = child.hierarchyEntry.sourceFile;
	            let group = groupsBySourceFile.get(sourceFile);
	            if (!group) {
	                group = [];
	                groupsBySourceFile.set(sourceFile, group);
	                insertionOrder.push(sourceFile);
	            }
	            group.push(child);
	        }
	        for (const sourceFile of insertionOrder) {
	            const group = groupsBySourceFile.get(sourceFile).sort((a, b) => {
	                const sheetIndexDiff = a.hierarchyEntry.schematicIndex - b.hierarchyEntry.schematicIndex;
	                if (sheetIndexDiff !== 0) {
	                    return sheetIndexDiff;
	                }
	                return a.channelIndex - b.channelIndex;
	            });
	            if (group.length <= 1 || group[0].totalSheetInstances <= 1) {
	                for (const child of group) {
	                    result.push(this.buildHierarchyNode(child));
	                }
	            }
	            else {
	                const transformer = this.schematicTransformers.get(sourceFile);
	                const overviewDesignId = transformer?.model.designId ?? sourceFile;
	                const instanceChildren = group.map((child) => this.buildHierarchyNode(child));
	                result.push({
	                    designId: overviewDesignId,
	                    label: `[${group[0].hierarchyEntry.schematicIndex + 1}] ${sigma_engine_shared_1.Files.basenamePath(sourceFile)}`,
	                    children: instanceChildren,
	                });
	            }
	        }
	        return result;
	    }
	    createInstanceCombobulator(instanceId) {
	        if (this.instances.has(instanceId)) {
	            throw new Error(`Instance ${instanceId} already has a combobulator`);
	        }
	        const instance = this.instanceMap.get(instanceId);
	        if (!instance) {
	            throw new Error(`Instance ${instanceId} not found in hierarchy`);
	        }
	        const netRefCtx = this.flatNamingContext ?? new sigma_engine_shared_1.NetRefCtx();
	        const combobulator = new NetCombobulator_1.NetCombobulator(netRefCtx, this.namingStrategy, instance, {
	            autoAssignNets: false,
	            pointRoundingPrecision: AltiumCombobulatorConfig_1.ALTIUM_POINT_ROUNDING_PRECISION,
	            connectionTolerance: AltiumCombobulatorConfig_1.ALTIUM_CONNECTION_TOLERANCE,
	        });
	        this.instances.set(instanceId, {
	            instanceId,
	            instance,
	            combobulator,
	            netRefCtx,
	        });
	        return combobulator;
	    }
	    getInstanceCombobulator(instanceId) {
	        const cb = this.instances.get(instanceId)?.combobulator;
	        if (!cb) {
	            throw new Error(`Instance ${instanceId} not found in hierarchy`);
	        }
	        return cb;
	    }
	    getInstanceInfo(pathUniqueId) {
	        const info = this.instances.get(pathUniqueId);
	        if (!info) {
	            throw new Error(`Instance ${pathUniqueId} not found in hierarchy`);
	        }
	        return info;
	    }
	    updateNetRefsToPhysicalNames(netGroups) {
	        for (const [groupId, group] of netGroups) {
	            if (!group.physicalName) {
	                throw new Error(`No physical name for net group ${groupId}`);
	            }
	            for (const [instance, nets] of group.getNets()) {
	                const instanceInfo = this.instances.get(instance.pathUniqueId);
	                if (!instanceInfo) {
	                    continue;
	                }
	                for (const net of nets) {
	                    const netRef = net.name;
	                    if (netRef.valueOf() !== group.physicalName) {
	                        netRef.setName(group.physicalName);
	                    }
	                    for (const ref of net.netRefs) {
	                        ref.setName(group.physicalName);
	                    }
	                }
	            }
	        }
	        for (const instanceInfo of this.instances.values()) {
	            instanceInfo.netRefCtx.clearOldRefs();
	        }
	    }
	}
	AltiumHierarchicalSchematicProcessor.AltiumHierarchicalSchematicProcessor = AltiumHierarchicalSchematicProcessor$1;
	
	return AltiumHierarchicalSchematicProcessor;
}

var hasRequiredParseAltiumProject;

function requireParseAltiumProject () {
	if (hasRequiredParseAltiumProject) return parseAltiumProject;
	hasRequiredParseAltiumProject = 1;
	Object.defineProperty(parseAltiumProject, "__esModule", { value: true });
	parseAltiumProject.parseProject = parseProject;
	const sigma_engine_shared_1 = requireDist$1();
	const altiumValidation_1 = requireAltiumValidation();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	const altiumProjectFileUtil_1 = requireAltiumProjectFileUtil();
	const altiumProjectFileTypes_1 = requireAltiumProjectFileTypes();
	const AltiumHierarchicalSchematicProcessor_1 = requireAltiumHierarchicalSchematicProcessor();
	const Font_1 = requireFont();
	const AltiumSchematic_1 = requireAltiumSchematic();
	const ModelText_1 = requireModelText();
	function parseProject(def, opts = {}) {
	    if (def.type !== 'altium') {
	        throw new Error(`Attempted to parse non-altium project def: ${def.type}`);
	    }
	    const buildDef = def;
	    const projectConfiguration = loadProjectConfigFromDef(buildDef) ?? (0, altiumProjectFileTypes_1.emptyAltiumProjectFile)();
	    const projectStructure = loadProjectStructureFromDef(buildDef);
	    const projectConfigUtil = new altiumProjectFileUtil_1.AltiumProjectFileUtil(projectConfiguration, projectStructure);
	    const processor = new AltiumHierarchicalSchematicProcessor_1.AltiumHierarchicalSchematicProcessor(def, projectConfigUtil, {
	        netNamingDebug: opts.netNamingDebug,
	    });
	    processor.buildInstanceMap();
	    processor.resolvePhysicalNetNames();
	    const projectModel = processor.getFinalModel();
	    projectModel.designHierarchy = processor.buildDesignHierarchy();
	    if (def.name) {
	        projectModel.name = def.name;
	    }
	    (0, Font_1.packModelFonts)(projectModel);
	    projectModel.processingResults.innerResults.push(...processor.processingResults);
	    const seenSchematics = new Set();
	    for (const schematicDef of Object.values(def.schematics)) {
	        if (schematicDef.validationResult?.isValid) {
	            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
	            if (schematic instanceof AltiumSchematic_1.AltiumSchematic && !seenSchematics.has(schematic)) {
	                seenSchematics.add(schematic);
	                projectModel.processingResults.innerResults.push(...schematic.processingResults);
	            }
	        }
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(projectModel.processingResults);
	    projectModel.modelVersion = ModelText_1.MODEL_TEXT_MIN_VERSION;
	    const extractedImages = processor.getExtractedImages();
	    const netNamingDebug = processor.getNetNamingDebug();
	    return {
	        projectModel: JSON.parse(JSON.stringify(projectModel)),
	        extractedImages,
	        ...(netNamingDebug === undefined ? {} : { netNamingDebug }),
	    };
	}
	function loadProjectConfigFromDef(buildDef) {
	    if (!buildDef.other) {
	        return undefined;
	    }
	    const projectFileDescriptor = Object.values(buildDef.other).find((file) => file.validationResult?.data?.metadata?.[altiumValidation_1.AltiumProjectMetadataKey.FileType] === altiumFileTypes_1.AltiumFileType.Project);
	    if (!projectFileDescriptor) {
	        return undefined;
	    }
	    return projectFileDescriptor.validationResult?.data?.metadata?.[altiumValidation_1.AltiumProjectMetadataKey.Project];
	}
	function loadProjectStructureFromDef(buildDef) {
	    if (!buildDef.other) {
	        return undefined;
	    }
	    const projectStructureDescriptor = Object.values(buildDef.other).find((file) => file.validationResult?.data?.metadata?.[altiumValidation_1.AltiumProjectMetadataKey.FileType] === altiumFileTypes_1.AltiumFileType.ProjectStructure);
	    if (!projectStructureDescriptor) {
	        return undefined;
	    }
	    return projectStructureDescriptor.validationResult?.data?.metadata?.[altiumValidation_1.AltiumProjectMetadataKey.ProjectStructure];
	}
	
	return parseAltiumProject;
}

var SchDocAsciiConverter = {};

var hasRequiredSchDocAsciiConverter;

function requireSchDocAsciiConverter () {
	if (hasRequiredSchDocAsciiConverter) return SchDocAsciiConverter;
	hasRequiredSchDocAsciiConverter = 1;
	Object.defineProperty(SchDocAsciiConverter, "__esModule", { value: true });
	SchDocAsciiConverter.SchDocAsciiConverter = void 0;
	const StreamParser_1 = requireStreamParser();
	const SchDocParser_1 = requireSchDocParser();
	const AsciiStreamParser_1 = requireAsciiStreamParser();
	const ASCII_SCHEMATIC_HEADER = 'Protel for Windows - Schematic Capture Ascii File Version 5.0';
	const BINARY_SCHEMATIC_HEADER = 'Protel for Windows - Schematic Capture Binary File Version 5.0';
	class SchDocAsciiConverter$1 {
	    static async convert(blob) {
	        const isCfb = await SchDocParser_1.SchDocParser.IsCfb(blob);
	        if (!isCfb) {
	            return blob.text();
	        }
	        const streamParser = await StreamParser_1.StreamParser.fromBlob(blob);
	        const fileHeaderRecords = await streamParser.parseFileHeader();
	        const additionalRecords = (await streamParser.parseAdditional()) ?? [];
	        const serializedFileHeader = fileHeaderRecords.map((props) => SchDocAsciiConverter$1.serializeRecord(props));
	        if (additionalRecords.length === 0) {
	            return serializedFileHeader.join('\r\n');
	        }
	        return [
	            ...serializedFileHeader,
	            AsciiStreamParser_1.ASCII_ICON_STORAGE_HEADER,
	            ...additionalRecords.map((props) => SchDocAsciiConverter$1.serializeRecord(props)),
	        ].join('\r\n');
	    }
	    static serializeRecord(properties) {
	        const parts = Object.values(properties)
	            .filter((prop) => prop !== undefined)
	            .map((prop) => {
	            const baseKey = prop.key.replace(/^%UTF8%/i, '');
	            const value = baseKey.toLowerCase() === 'header' && prop.value === BINARY_SCHEMATIC_HEADER ?
	                ASCII_SCHEMATIC_HEADER
	                : prop.value;
	            const key = SchDocAsciiConverter$1.hasNonAscii(value) ? `%UTF8%${baseKey}` : baseKey;
	            return `${key}=${value}`;
	        });
	        return '|' + parts.join('|');
	    }
	    static hasNonAscii(value) {
	        for (let i = 0; i < value.length; i++) {
	            if (value.charCodeAt(i) > 0x7f) {
	                return true;
	            }
	        }
	        return false;
	    }
	}
	SchDocAsciiConverter.SchDocAsciiConverter = SchDocAsciiConverter$1;
	
	return SchDocAsciiConverter;
}

var hasRequiredAltium;

function requireAltium () {
	if (hasRequiredAltium) return Altium;
	hasRequiredAltium = 1;
	(function (exports) {
		var __createBinding = (Altium && Altium.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Altium && Altium.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SchDocAsciiConverter = void 0;
		__exportStar(requireAltiumHarness(), exports);
		__exportStar(requireAltiumValidation(), exports);
		__exportStar(requireAltiumRequiredFiles(), exports);
		__exportStar(requireAltiumFileTypes(), exports);
		__exportStar(requireAltiumRecordFile(), exports);
		__exportStar(requireProject(), exports);
		__exportStar(requireParseAltiumProject(), exports);
		__exportStar(requireApplyNetlistToProject(), exports);
		__exportStar(requireAltiumTextCodec(), exports);
		var SchDocAsciiConverter_1 = requireSchDocAsciiConverter();
		Object.defineProperty(exports, "SchDocAsciiConverter", { enumerable: true, get: function () { return SchDocAsciiConverter_1.SchDocAsciiConverter; } });
		
	} (Altium));
	return Altium;
}

var OrCAD = {};

var orcadValidation = {};

var opj = {};

var OPJTypes = {};

var hasRequiredOPJTypes;

function requireOPJTypes () {
	if (hasRequiredOPJTypes) return OPJTypes;
	hasRequiredOPJTypes = 1;
	Object.defineProperty(OPJTypes, "__esModule", { value: true });
	
	return OPJTypes;
}

var OPJParser = {};

var OrCADParsingResult = {};

var hasRequiredOrCADParsingResult;

function requireOrCADParsingResult () {
	if (hasRequiredOrCADParsingResult) return OrCADParsingResult;
	hasRequiredOrCADParsingResult = 1;
	Object.defineProperty(OrCADParsingResult, "__esModule", { value: true });
	OrCADParsingResult.OrCADParsingMessageType = void 0;
	var OrCADParsingMessageType;
	(function (OrCADParsingMessageType) {
	    OrCADParsingMessageType["UnknownProperty"] = "orcad-unknown-property";
	    OrCADParsingMessageType["UnknownNode"] = "orcad-unknown-node";
	    OrCADParsingMessageType["UnexpectedValue"] = "orcad-unexpected-value";
	    OrCADParsingMessageType["ParsingWarning"] = "orcad-parsing-warning";
	})(OrCADParsingMessageType || (OrCADParsingResult.OrCADParsingMessageType = OrCADParsingMessageType = {}));
	
	return OrCADParsingResult;
}

var hasRequiredOPJParser;

function requireOPJParser () {
	if (hasRequiredOPJParser) return OPJParser;
	hasRequiredOPJParser = 1;
	Object.defineProperty(OPJParser, "__esModule", { value: true });
	OPJParser.parseOrCADCaptureProject = parseOrCADCaptureProject;
	OPJParser.normalizePropertyName = normalizePropertyName;
	OPJParser.getProperty = getProperty;
	const NodeParser_1 = requireNodeParser();
	const BoundaryLevel_1 = requireBoundaryLevel();
	const OrCADParsingResult_1 = requireOrCADParsingResult();
	const KNOWN_DESIGN_RESOURCES_PROPERTIES = new Set([
	    'BuildFileAddedOrDeleted',
	    'CompileFileAddedOrDeleted',
	    'NoModify',
	    '"Create Allegro Netlist"',
	    '"Allegro Netlist Directory"',
	    '"View Allegro Netlist Files"',
	    '"Allegro Netlist Create DCF File"',
	    '"Update Allegro Board"',
	    '"Allegro Netlist Input Board File"',
	    '"Allegro Netlist Output Board File"',
	    '"Allegro Netlist Remove Etch"',
	    '"Allegro Netlist Place Changed Component"',
	    '"Allegro Netlist Open Board in Allegro"',
	    '"Allegro Setup Backup Versions"',
	    '"Allegro Setup Output Warnings"',
	    '"Allegro Setup Ignore Constraints"',
	    '"Allegro Setup Part Type Length"',
	    '"Allegro Netlist Combine Property String"',
	    '"Allegro Netlist Ignore Fixed Property"',
	    '"Allegro Netlist User Defined Property"',
	    'Netlist_TAB',
	    '"Allegro Setup Configuration File"',
	    'DOCKED',
	    'DOCKING_POSITION',
	    'Board_sim_option',
	    'OTHER_Part_Value',
	    'OTHER_Netlist_File',
	    'OTHER_Netlist_File2',
	    'OTHER_View_Output',
	    'OTHER_View_Output2',
	    'OTHER_Formatter',
	    'OTHER_PCB_Footprint',
	    'OTHER_Switch0',
	    'OTHER_Switch1',
	    'OTHER_Switch2',
	    'OTHER_Switch3',
	    'OTHER_Switch4',
	    'OTHER_Switch5',
	    'OTHER_Switch6',
	]);
	const KNOWN_TOP_LEVEL_NODES = new Set([
	    'ProjectVersion',
	    'SoftwareVersion',
	    'ProjectType',
	    'Folder',
	    'PartMRUSelector',
	    'GlobalState',
	    'MPSSessionName',
	    'ISPCBBASICLICENSE',
	]);
	function parseOrCADCaptureProject(contents) {
	    const rootNode = NodeParser_1.SExpressionFileParser.Parse(contents);
	    if (rootNode.name !== 'ExpressProject') {
	        throw new Error('Invalid OrCAD Capture project file: root node must be ExpressProject');
	    }
	    const context = {
	        warnings: [],
	    };
	    const project = {
	        folders: [],
	    };
	    const processingResults = {
	        type: 'orcad-parsing',
	        message: 'OrCAD project parsing completed',
	        innerResults: [],
	        severity: BoundaryLevel_1.BoundaryLevel.Recommendation,
	    };
	    for (const node of rootNode.nodes) {
	        switch (node.name) {
	            case 'ProjectVersion':
	                project.projectVersion = parseQuotedValue(node.values[0]);
	                break;
	            case 'SoftwareVersion':
	                project.softwareVersion = parseQuotedValue(node.values[0]);
	                break;
	            case 'ProjectType':
	                project.projectType = parseQuotedValue(node.values[0]);
	                break;
	            case 'Folder':
	                project.folders.push(parseFolder(node, context));
	                break;
	            case 'PartMRUSelector':
	                project.partMRUSelector = parsePartMRUSelector(node, context);
	                break;
	            case 'GlobalState':
	                project.globalState = parseGlobalState(node, context);
	                break;
	            case 'MPSSessionName':
	                project.mpsSessionName = parseQuotedValue(node.values[0]);
	                break;
	            case 'ISPCBBASICLICENSE':
	                project.isPCBBasicLicense = parseQuotedValue(node.values[0]) === 'true';
	                break;
	            default:
	                if (!KNOWN_TOP_LEVEL_NODES.has(node.name)) {
	                    context.warnings.push({
	                        type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                        message: `Unknown top-level node: ${node.name}`,
	                        severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                        nodeName: node.name,
	                        nodeValues: node.values,
	                    });
	                }
	                break;
	        }
	    }
	    processingResults.innerResults = context.warnings;
	    if (context.warnings.length > 0) {
	        const hasErrors = context.warnings.some((w) => w.severity === BoundaryLevel_1.BoundaryLevel.Error);
	        const hasWarnings = context.warnings.some((w) => w.severity === BoundaryLevel_1.BoundaryLevel.Warning);
	        processingResults.severity =
	            hasErrors ? BoundaryLevel_1.BoundaryLevel.Error
	                : hasWarnings ? BoundaryLevel_1.BoundaryLevel.Warning
	                    : BoundaryLevel_1.BoundaryLevel.Recommendation;
	        processingResults.message = `OrCAD project parsing completed with ${context.warnings.length} warning(s)`;
	    }
	    else {
	        processingResults.severity = BoundaryLevel_1.BoundaryLevel.Recommendation;
	    }
	    return {
	        project,
	        processingResults,
	    };
	}
	function parseFolder(node, context) {
	    const folder = {
	        name: parseQuotedValue(node.values[0]) || '',
	        folders: [],
	        files: [],
	        properties: {},
	    };
	    const folderName = folder.name;
	    const isDesignResourcesFolder = folderName === 'Design Resources';
	    for (const childNode of node.nodes) {
	        switch (childNode.name) {
	            case 'Folder':
	                folder.folders.push(parseFolder(childNode, context));
	                break;
	            case 'File':
	                folder.files.push(parseFile(childNode, context));
	                break;
	            case 'Sort':
	                folder.sort = parseQuotedValue(childNode.values[0]);
	                break;
	            default:
	                if (childNode.values.length > 0) {
	                    const key = parseQuotedValue(childNode.name) || childNode.name;
	                    const value = parseQuotedValue(childNode.values[0]);
	                    folder.properties[key] = value;
	                    const checkKey = childNode.name;
	                    if (isDesignResourcesFolder && !KNOWN_DESIGN_RESOURCES_PROPERTIES.has(checkKey)) {
	                        context.warnings.push({
	                            type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownProperty,
	                            message: `Unknown property in Design Resources folder: ${key}`,
	                            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                            folderName,
	                            propertyName: key,
	                            propertyValue: value,
	                        });
	                    }
	                }
	                else {
	                    const key = parseQuotedValue(childNode.name) || childNode.name;
	                    folder.properties[key] = true;
	                    const checkKey = childNode.name;
	                    if (isDesignResourcesFolder && !KNOWN_DESIGN_RESOURCES_PROPERTIES.has(checkKey)) {
	                        context.warnings.push({
	                            type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownProperty,
	                            message: `Unknown boolean property in Design Resources folder: ${key}`,
	                            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                            folderName,
	                            propertyName: key,
	                            propertyValue: true,
	                        });
	                    }
	                }
	                break;
	        }
	    }
	    return folder;
	}
	function parseFile(node, context) {
	    const file = {
	        path: parseQuotedValue(node.values[0]) || '',
	        type: '',
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'Type') {
	            file.type = parseQuotedValue(childNode.values[0]) || '';
	        }
	        else {
	            context.warnings.push({
	                type: 'orcad-unknown-property',
	                message: `Unknown file property: ${childNode.name}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                fileName: file.path,
	                propertyName: childNode.name,
	            });
	        }
	    }
	    return file;
	}
	function parsePartMRUSelector(node, context) {
	    const selector = {
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.values.length > 0) {
	            selector.properties[childNode.name] = parseQuotedValue(childNode.values[0]);
	        }
	        else {
	            context.warnings.push({
	                type: 'orcad-unknown-node',
	                message: `Unexpected content in PartMRUSelector: ${childNode.name}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                nodeName: childNode.name,
	            });
	        }
	    }
	    return selector;
	}
	function parseGlobalState(node, context) {
	    const globalState = {
	        documents: [],
	    };
	    for (const childNode of node.nodes) {
	        switch (childNode.name) {
	            case 'FileView':
	                globalState.fileView = parseFileView(childNode, context);
	                break;
	            case 'HierarchyView':
	                globalState.hierarchyView = { properties: {} };
	                break;
	            case 'Doc':
	                globalState.documents.push(parseDocument(childNode, context));
	                break;
	            default:
	                context.warnings.push({
	                    type: 'orcad-unknown-node',
	                    message: `Unknown GlobalState content: ${childNode.name}`,
	                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                    nodeName: childNode.name,
	                });
	                break;
	        }
	    }
	    return globalState;
	}
	function parseFileView(node, context) {
	    const fileView = {
	        paths: [],
	    };
	    for (const childNode of node.nodes) {
	        switch (childNode.name) {
	            case 'Path': {
	                fileView.paths.push(parseFileViewPath(childNode));
	                break;
	            }
	            case 'Select': {
	                fileView.selection = {
	                    path: childNode.values.map((v) => parseQuotedValue(v)),
	                };
	                break;
	            }
	            default:
	                context.warnings.push({
	                    type: 'orcad-unknown-node',
	                    message: `Unknown FileView content: ${childNode.name}`,
	                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                    nodeName: childNode.name,
	                });
	                break;
	        }
	    }
	    return fileView;
	}
	function parseFileViewPath(node, context) {
	    const [editorFolder, fsPath, ...folderPosition] = node.values;
	    const path = {
	        editorFolder: parseQuotedValue(editorFolder),
	        fsPath: fsPath ? parseQuotedValue(fsPath) : undefined,
	        folderPosition: folderPosition.map((v) => parseQuotedValue(v)),
	    };
	    return path;
	}
	function parseDocument(node, context) {
	    const document = {
	        type: '',
	    };
	    for (const childNode of node.nodes) {
	        switch (childNode.name) {
	            case 'Type':
	                document.type = parseQuotedValue(childNode.values[0]) || '';
	                break;
	            case 'Frame':
	                document.frame = parseDocumentFrame(childNode, context);
	                break;
	            case 'Tab':
	                document.tab = parseInt(childNode.values[0], 10);
	                break;
	            case 'Path':
	                document.path = parseQuotedValue(childNode.values[0]);
	                break;
	            case 'Schematic':
	                document.schematic = parseQuotedValue(childNode.values[0]);
	                break;
	            case 'Page':
	                document.page = parseQuotedValue(childNode.values[0]);
	                break;
	            default:
	                context.warnings.push({
	                    type: 'orcad-unknown-node',
	                    message: `Unknown Document content: ${childNode.name}`,
	                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                    nodeName: childNode.name,
	                });
	                break;
	        }
	    }
	    return document;
	}
	function parseDocumentFrame(node, context) {
	    const frame = {};
	    for (const childNode of node.nodes) {
	        switch (childNode.name) {
	            case 'Placement':
	                frame.placement = parseQuotedValue(childNode.values[0]);
	                break;
	            case 'Scroll':
	                frame.scroll = parseQuotedValue(childNode.values[0]);
	                break;
	            case 'Zoom':
	                frame.zoom = parseQuotedValue(childNode.values[0]);
	                break;
	            case 'Occurrence':
	                frame.occurrence = parseQuotedValue(childNode.values[0]);
	                break;
	            default:
	                context.warnings.push({
	                    type: 'orcad-unknown-node',
	                    message: `Unknown DocumentFrame content: ${childNode.name}`,
	                    severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                    nodeName: childNode.name,
	                });
	                break;
	        }
	    }
	    return frame;
	}
	function parseQuotedValue(value) {
	    if (!value) {
	        return '';
	    }
	    if (value.startsWith('"') && value.endsWith('"')) {
	        return value.slice(1, -1);
	    }
	    return value;
	}
	function normalizePropertyName(name) {
	    return parseQuotedValue(name);
	}
	function getProperty(properties, key) {
	    if (Object.prototype.hasOwnProperty.call(properties, key)) {
	        return properties[key];
	    }
	    const quotedKey = `"${key}"`;
	    if (Object.prototype.hasOwnProperty.call(properties, quotedKey)) {
	        return properties[quotedKey];
	    }
	    if (key.startsWith('"') && key.endsWith('"')) {
	        const unquotedKey = key.slice(1, -1);
	        if (Object.prototype.hasOwnProperty.call(properties, unquotedKey)) {
	            return properties[unquotedKey];
	        }
	    }
	    return undefined;
	}
	
	return OPJParser;
}

var hasRequiredOpj;

function requireOpj () {
	if (hasRequiredOpj) return opj;
	hasRequiredOpj = 1;
	(function (exports) {
		var __createBinding = (opj && opj.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (opj && opj.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireOPJTypes(), exports);
		__exportStar(requireOPJParser(), exports);
		
	} (opj));
	return opj;
}

var ONLParser = {};

var hasRequiredONLParser;

function requireONLParser () {
	if (hasRequiredONLParser) return ONLParser;
	hasRequiredONLParser = 1;
	Object.defineProperty(ONLParser, "__esModule", { value: true });
	ONLParser.parseOrCADNetlist = parseOrCADNetlist;
	const NodeParser_1 = requireNodeParser();
	const BoundaryLevel_1 = requireBoundaryLevel();
	const OrCADParsingResult_1 = requireOrCADParsingResult();
	const Util_1 = requireUtil();
	const KNOWN_TOP_LEVEL_NODES = new Set(['description', 'structure', 'placement', 'library', 'network']);
	const KNOWN_DESCRIPTION_PROPERTIES = new Set([
	    'timeStamp',
	    'program',
	    'source',
	    'title',
	    'date',
	    'document',
	    'revision',
	    'organization',
	    'address1',
	    'address2',
	    'address3',
	    'address4',
	    'partvaluecombine',
	    'pcbfootprintcombine',
	]);
	function parseOrCADNetlist(contents) {
	    const context = {
	        warnings: [],
	    };
	    try {
	        const rootNode = NodeParser_1.SExpressionFileParser.Parse(contents);
	        if (rootNode.name !== 'PCB') {
	            context.warnings.push({
	                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                message: `Expected root node 'PCB', found '${rootNode.name}'`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                nodeName: rootNode.name,
	            });
	        }
	        const netlist = {
	            name: (0, Util_1.removeQuotes)(rootNode.values[0]),
	        };
	        for (const childNode of rootNode.nodes) {
	            switch (childNode.name) {
	                case 'description':
	                    netlist.description = parseDescription(childNode, context);
	                    break;
	                case 'structure':
	                    netlist.structure = parseStructure(childNode, context);
	                    break;
	                case 'placement':
	                    netlist.placement = parsePlacement(childNode, context);
	                    break;
	                case 'library':
	                    netlist.library = parseLibrary(childNode, context);
	                    break;
	                case 'network':
	                    netlist.network = parseNetwork(childNode, context);
	                    break;
	                default:
	                    if (!KNOWN_TOP_LEVEL_NODES.has(childNode.name)) {
	                        context.warnings.push({
	                            type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                            message: `Unknown top-level node: ${childNode.name}`,
	                            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                            nodeName: childNode.name,
	                        });
	                    }
	                    break;
	            }
	        }
	        const processingResults = {
	            type: 'orcad-parsing',
	            message: context.warnings.length > 0 ?
	                `OrCAD netlist parsing completed with ${context.warnings.length} warning(s)`
	                : 'OrCAD netlist parsing completed',
	            innerResults: context.warnings,
	            severity: context.warnings.length > 0 ? BoundaryLevel_1.BoundaryLevel.Warning : BoundaryLevel_1.BoundaryLevel.Recommendation,
	        };
	        return {
	            netlist,
	            processingResults,
	        };
	    }
	    catch (error) {
	        const processingResults = {
	            type: 'orcad-parsing',
	            message: `Failed to parse OrCAD netlist: ${error instanceof Error ? error.message : 'Unknown error'}`,
	            innerResults: [],
	            severity: BoundaryLevel_1.BoundaryLevel.Error,
	        };
	        return {
	            netlist: { name: '' },
	            processingResults,
	        };
	    }
	}
	function parseDescription(node, context) {
	    const description = {
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        switch (childNode.name) {
	            case 'timeStamp':
	                description.timeStamp = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'program':
	                description.program = parseProgram(childNode);
	                break;
	            case 'source':
	                description.source = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'title':
	                description.title = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'date':
	                description.date = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'document':
	                description.document = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'revision':
	                description.revision = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'organization':
	                description.organization = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'address1':
	                description.address1 = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'address2':
	                description.address2 = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'address3':
	                description.address3 = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'address4':
	                description.address4 = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'partvaluecombine':
	                description.partvaluecombine = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            case 'pcbfootprintcombine':
	                description.pcbfootprintcombine = (0, Util_1.removeQuotes)(childNode.values[0]);
	                break;
	            default:
	                if (childNode.values.length > 0) {
	                    const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	                    const value = (0, Util_1.removeQuotes)(childNode.values[0]);
	                    description.properties[key] = value;
	                }
	                else {
	                    const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	                    description.properties[key] = true;
	                }
	                if (!KNOWN_DESCRIPTION_PROPERTIES.has(childNode.name)) {
	                    context.warnings.push({
	                        type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownProperty,
	                        message: `Unknown property in description: ${childNode.name}`,
	                        severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                        propertyName: childNode.name,
	                    });
	                }
	                break;
	        }
	    }
	    return description;
	}
	function parseProgram(node, context) {
	    const program = {
	        name: (0, Util_1.removeQuotes)(node.values[0]),
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'Version') {
	            program.version = (0, Util_1.removeQuotes)(childNode.values[0]);
	        }
	    }
	    return program;
	}
	function parseStructure(node, context) {
	    const structure = {
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.values.length > 0) {
	            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	            const value = (0, Util_1.removeQuotes)(childNode.values[0]);
	            structure.properties[key] = value;
	        }
	        else {
	            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	            structure.properties[key] = true;
	        }
	    }
	    return structure;
	}
	function parsePlacement(node, context) {
	    const placement = {
	        components: [],
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'component') {
	            const component = parseComponent(childNode, context);
	            placement.components.push(component);
	        }
	        else {
	            context.warnings.push({
	                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                message: `Unknown node in placement: ${childNode.name}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                nodeName: childNode.name,
	            });
	        }
	    }
	    return placement;
	}
	function parseComponent(node, context) {
	    const component = {
	        name: (0, Util_1.removeQuotes)(node.values[0]),
	        places: [],
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'place') {
	            const place = parsePlace(childNode, context);
	            component.places.push(place);
	        }
	        else {
	            context.warnings.push({
	                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                message: `Unknown node in component ${component.name}: ${childNode.name}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                nodeName: childNode.name,
	                componentName: component.name,
	            });
	        }
	    }
	    return component;
	}
	function parsePlace(node, context) {
	    const place = {
	        refdes: (0, Util_1.removeQuotes)(node.values[0]),
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'property') {
	            parseProperties(childNode, place.properties);
	        }
	        else {
	            context.warnings.push({
	                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                message: `Unknown node in place ${place.refdes}: ${childNode.name}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                nodeName: childNode.name,
	                refdes: place.refdes,
	            });
	        }
	    }
	    return place;
	}
	function parseProperties(node, properties, context) {
	    for (const childNode of node.nodes) {
	        const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	        if (childNode.values.length > 0) {
	            const rawValue = (0, Util_1.removeQuotes)(childNode.values[0]);
	            const numericValue = parseFloat(rawValue);
	            if (!isNaN(numericValue) && isFinite(numericValue) && rawValue.trim() === numericValue.toString()) {
	                properties[key] = numericValue;
	            }
	            else {
	                properties[key] = rawValue;
	            }
	        }
	        else {
	            properties[key] = true;
	        }
	    }
	}
	function parseLibrary(node, context) {
	    const library = {
	        entries: [],
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.values.length > 0) {
	            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	            const value = (0, Util_1.removeQuotes)(childNode.values[0]);
	            library.properties[key] = value;
	        }
	        else {
	            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	            library.properties[key] = true;
	        }
	    }
	    return library;
	}
	function parseNetwork(node, context) {
	    const nets = [];
	    const network = {
	        nets,
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'net') {
	            const net = parseNetworkNet(childNode, context);
	            if (net) {
	                nets.push(net);
	            }
	        }
	        else {
	            if (childNode.values.length > 0) {
	                const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	                const value = (0, Util_1.removeQuotes)(childNode.values[0]);
	                network.properties[key] = value;
	            }
	            else {
	                const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
	                network.properties[key] = true;
	            }
	        }
	    }
	    return network;
	}
	function parseNetworkNet(node, context) {
	    if (node.name !== 'net') {
	        throw new Error(`Expected net node, found ${node.name}`);
	    }
	    if (node.values.length !== 1) {
	        context.warnings.push({
	            type: OrCADParsingResult_1.OrCADParsingMessageType.ParsingWarning,
	            message: `Invalid net definition: ${node.values.join(' ')}`,
	            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	        });
	        return;
	    }
	    const net = {
	        name: (0, Util_1.removeQuotes)(node.values[0]),
	        pins: [],
	        properties: {},
	    };
	    for (const childNode of node.nodes) {
	        if (childNode.name === 'pins') {
	            net.pins.push(...parseNetworkPins(childNode, context));
	        }
	        else if (childNode.name === 'property') {
	            parseProperties(childNode, net.properties);
	        }
	        else {
	            context.warnings.push({
	                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
	                message: `Unknown node in net ${net.name}: ${childNode.name}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	                nodeName: childNode.name,
	                netName: net.name,
	            });
	        }
	    }
	    return net;
	}
	function parseNetworkPins(node, context) {
	    if (node.name !== 'pins') {
	        throw new Error(`Expected pin node, found ${node.name}`);
	    }
	    return node.values
	        .map((pinStr) => {
	        const firstIndex = pinStr.indexOf('-');
	        if (firstIndex === -1) {
	            context.warnings.push({
	                type: OrCADParsingResult_1.OrCADParsingMessageType.ParsingWarning,
	                message: `Invalid pin definition: ${pinStr}, missing '-' separator`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	            });
	            return;
	        }
	        const referenceDesignator = pinStr.substring(0, firstIndex);
	        let pin = pinStr.substring(firstIndex + 1);
	        if (pin.startsWith('"') && pin.endsWith('"')) {
	            pin = pin.substring(1, pin.length - 1);
	        }
	        return {
	            referenceDesignator,
	            pin,
	        };
	    })
	        .filter((pin) => pin !== undefined);
	}
	
	return ONLParser;
}

var xmlutil = {};

var hasRequiredXmlutil;

function requireXmlutil () {
	if (hasRequiredXmlutil) return xmlutil;
	hasRequiredXmlutil = 1;
	Object.defineProperty(xmlutil, "__esModule", { value: true });
	xmlutil.ParseXml = ParseXml;
	xmlutil.adaptSimpleAttributes = adaptSimpleAttributes;
	xmlutil.xmlBool = xmlBool;
	xmlutil.xmlNum = xmlNum;
	xmlutil.xmlInt = xmlInt;
	const xml_js_1 = requireLib$1z();
	function ParseXml(input) {
	    const tree = (0, xml_js_1.xml2js)(input, { compact: true });
	    inlineAttributesInPlace(tree);
	    return tree;
	}
	function inlineAttributesInPlace(input) {
	    if (input === null || typeof input !== 'object') {
	        return;
	    }
	    if (Array.isArray(input)) {
	        for (const item of input) {
	            inlineAttributesInPlace(item);
	        }
	        return;
	    }
	    const attrs = input._attributes;
	    if (attrs && typeof attrs === 'object') {
	        for (const key of Object.keys(attrs)) {
	            input[`$${key}`] = attrs[key];
	        }
	    }
	    for (const key of Object.keys(input)) {
	        if (key.charCodeAt(0) === 36 || key === '_attributes') {
	            continue;
	        }
	        const value = input[key];
	        if (typeof value !== 'object') {
	            throw new Error(`Unexpected value type ${typeof value} for key ${key}`);
	        }
	        inlineAttributesInPlace(value);
	    }
	}
	function adaptSimpleAttributes(attrs, obj, typename, requireAllAttrs = false) {
	    let foundAttrs = 0;
	    const result = {};
	    for (const key of Object.keys(obj)) {
	        if (!key.startsWith('$')) {
	            continue;
	        }
	        const attr = key.substring(1);
	        if (!attrs.str[attr] && !attrs.num[attr] && !attrs.bool[attr]) {
	            throw new Error(`Unexpected key ${key} in ${typename}`);
	        }
	        ++foundAttrs;
	        result[attr] = obj[key];
	    }
	    if (requireAllAttrs && foundAttrs !== attrs.length) {
	        throw new Error(`Missing keys in ${typename}`);
	    }
	    return result;
	}
	function xmlBool(value) {
	    if (value === undefined) {
	        return undefined;
	    }
	    if (typeof value === 'string') {
	        return value === '1';
	    }
	    if (typeof value === 'number') {
	        return value === 1;
	    }
	    if (typeof value === 'boolean') {
	        return value;
	    }
	    throw new Error(`Unexpected value for boolean type ${typeof value}`);
	}
	function xmlNum(value) {
	    if (typeof value === 'string') {
	        value = value.trim();
	        if (!/^(\+|-)?\d*(\.\d*)?$/.test(value)) {
	            throw new Error(`Expected number value, got ${value}`);
	        }
	        return parseFloat(value);
	    }
	    if (typeof value === 'number') {
	        return value;
	    }
	    throw new Error(`Unexpected value for number type ${typeof value}`);
	}
	function xmlInt(value) {
	    if (typeof value === 'string') {
	        value = value.trim();
	        if (!/^(\+|-)?\d+$/.test(value)) {
	            throw new Error(`Expected integer value, got ${value}`);
	        }
	        return parseInt(value);
	    }
	    if (typeof value === 'number') {
	        return value;
	    }
	    throw new Error(`Unexpected value for number type ${typeof value}`);
	}
	
	return xmlutil;
}

var LogicalSchemaClasses = {};

var xmlTransformer = {};

var hasRequiredXmlTransformer;

function requireXmlTransformer () {
	if (hasRequiredXmlTransformer) return xmlTransformer;
	hasRequiredXmlTransformer = 1;
	Object.defineProperty(xmlTransformer, "__esModule", { value: true });
	xmlTransformer.TransformAggregateError = xmlTransformer.EnumArg = xmlTransformer.OptionalArg = void 0;
	xmlTransformer.maybeApplyProps = maybeApplyProps;
	xmlTransformer.transformXmlArgs = transformXmlArgs;
	xmlTransformer.parseTransformer = parseTransformer;
	xmlTransformer.mergeDfn = mergeDfn;
	const sigma_engine_shared_1 = requireDist$1();
	const xmlutil_1 = requireXmlutil();
	class OptionalArg {
	    constructor(type) {
	        this.type = type;
	    }
	}
	xmlTransformer.OptionalArg = OptionalArg;
	class EnumArg {
	    constructor(values) {
	        this.values = values;
	    }
	    static FromNativeEnum(nativeEnum) {
	        return new EnumArg(Object.values(nativeEnum));
	    }
	    FromXml(value) {
	        if (!this.values.includes(value)) {
	            throw new Error(`Value ${value} is not a valid enum value. Expected one of '${this.values.join(', ')}'`);
	        }
	        return value;
	    }
	}
	xmlTransformer.EnumArg = EnumArg;
	function maybeApplyProps(target, from) {
	    if (from) {
	        Object.assign(target, from);
	    }
	}
	class TransformAggregateError extends AggregateError {
	    constructor(errors, innerMessage, key) {
	        super(errors, `Error transforming key ${key}: ${innerMessage}`);
	        this.errors = errors;
	        this.innerMessage = innerMessage;
	        this.key = key;
	        Object.setPrototypeOf(this, TransformAggregateError.prototype);
	    }
	}
	xmlTransformer.TransformAggregateError = TransformAggregateError;
	const KIND_STRING = 1;
	const KIND_NUMBER = 2;
	const KIND_BOOLEAN = 3;
	const KIND_ENUM = 4;
	const KIND_CLASS = 5;
	const KIND_FN = 6;
	const compiledSpecCache = new WeakMap();
	function compileEntry(propKey, xmlKey, raw) {
	    let spec = raw;
	    let isOptional = false;
	    if (raw instanceof OptionalArg) {
	        isOptional = true;
	        spec = raw.type;
	    }
	    let isArray = false;
	    let inner;
	    if (Array.isArray(spec)) {
	        if (spec.length !== 1) {
	            throw new Error(`Array transform spec must have exactly one element`);
	        }
	        isArray = true;
	        inner = spec[0];
	    }
	    else {
	        inner = spec;
	    }
	    let kind;
	    let payload = null;
	    if (inner === 'string') {
	        kind = KIND_STRING;
	    }
	    else if (inner === 'number') {
	        kind = KIND_NUMBER;
	    }
	    else if (inner === 'boolean') {
	        kind = KIND_BOOLEAN;
	    }
	    else if (inner instanceof EnumArg) {
	        kind = KIND_ENUM;
	        payload = inner;
	    }
	    else if (typeof inner === 'function') {
	        if (typeof inner.FromXml === 'function') {
	            kind = KIND_CLASS;
	            payload = inner;
	        }
	        else {
	            kind = KIND_FN;
	            payload = inner;
	        }
	    }
	    else {
	        throw new Error(`Unknown transform spec instruction: ${String(inner)}`);
	    }
	    return { propKey, xmlKey, kind, payload, isOptional, isArray };
	}
	function compileSpec(transformSpec) {
	    const cached = compiledSpecCache.get(transformSpec);
	    if (cached !== undefined) {
	        return cached;
	    }
	    const entries = [];
	    for (const propKey of Object.keys(transformSpec)) {
	        const tuple = transformSpec[propKey];
	        entries.push(compileEntry(propKey, tuple[0], tuple[1]));
	    }
	    compiledSpecCache.set(transformSpec, entries);
	    return entries;
	}
	function applyInstruction(entry, parent, key, opts) {
	    const value = parent[key];
	    switch (entry.kind) {
	        case KIND_STRING:
	            if (typeof value !== 'string') {
	                throw new Error(`Expected string value for key ${String(key)}, got ${typeof value}`);
	            }
	            return value;
	        case KIND_NUMBER:
	            return (0, xmlutil_1.xmlNum)(value);
	        case KIND_BOOLEAN:
	            return (0, xmlutil_1.xmlBool)(value);
	        case KIND_ENUM:
	            return entry.payload.FromXml(value);
	        case KIND_CLASS:
	            return entry.payload.FromXml(value, opts);
	        case KIND_FN:
	            return entry.payload(parent, key);
	        default:
	            throw new Error(`Unknown compiled kind ${entry.kind}`);
	    }
	}
	function transformXmlArgs(xmlData, opts, transformSpec, allowExtraKeys = false) {
	    const unDefnd = mergeDfn(xmlData);
	    const compiled = compileSpec(transformSpec);
	    const result = {};
	    let consumed = 0;
	    for (let i = 0; i < compiled.length; i++) {
	        const entry = compiled[i];
	        const xmlKey = entry.xmlKey;
	        const present = xmlKey in unDefnd;
	        const rawValue = present ? unDefnd[xmlKey] : undefined;
	        if (!present || rawValue === undefined || rawValue === null) {
	            if (present) {
	                consumed++;
	            }
	            if (entry.isOptional) {
	                result[entry.propKey] = null;
	                continue;
	            }
	            if (!entry.isArray) {
	                console.error('Missing required key', xmlKey, 'in', unDefnd);
	            }
	            throw new TransformAggregateError([new Error(`Missing required key ${xmlKey}`)], `Missing required key ${xmlKey}`, entry.propKey);
	        }
	        consumed++;
	        try {
	            if (entry.isArray) {
	                const arr = sigma_engine_shared_1.Utils.toArray(rawValue);
	                const len = arr.length;
	                const out = new Array(len);
	                for (let j = 0; j < len; j++) {
	                    const elem = arr[j];
	                    if (elem === undefined || elem === null) {
	                        throw new Error(`Missing required key ${j}`);
	                    }
	                    out[j] = applyInstruction(entry, arr, j, opts);
	                }
	                result[entry.propKey] = out;
	            }
	            else {
	                result[entry.propKey] = applyInstruction(entry, unDefnd, xmlKey, opts);
	            }
	        }
	        catch (e) {
	            if (e instanceof TransformAggregateError) {
	                throw new TransformAggregateError(e.errors, e.innerMessage, `${entry.propKey}.${e.key}`);
	            }
	            if (e instanceof Error) {
	                throw new TransformAggregateError([e], e.message, entry.propKey);
	            }
	            throw e;
	        }
	    }
	    if (!allowExtraKeys) {
	        let totalKeys = 0;
	        for (const _ in unDefnd) {
	            totalKeys++;
	        }
	        if ('_attributes' in unDefnd) {
	            totalKeys--;
	        }
	        if (totalKeys !== consumed) {
	            const xmlKeys = new Set(Object.keys(unDefnd));
	            xmlKeys.delete('_attributes');
	            for (let i = 0; i < compiled.length; i++) {
	                xmlKeys.delete(compiled[i].xmlKey);
	            }
	            console.error('Extra keys in XML data:', Array.from(xmlKeys).join(', '), JSON.stringify(Object.fromEntries(Array.from(Object.entries(unDefnd)).filter(([k]) => xmlKeys.has(k)))));
	            throw new Error(`Extra keys in XML data: ${Array.from(xmlKeys).join(', ')}`);
	        }
	    }
	    return result;
	}
	function parseTransformer(rawTransformer, opts) {
	    function transformSpecInstruction(specInstruction) {
	        return function singleItemTransformer(xml, key) {
	            const isOptional = rawTransformer instanceof OptionalArg;
	            if (!(key in xml) || xml[key] === undefined || xml[key] === null) {
	                if (isOptional) {
	                    return null;
	                }
	                console.error('Missing required key', String(key), 'in', xml);
	                throw new Error(`Missing required key ${String(key)}`);
	            }
	            if (specInstruction === 'string') {
	                if (typeof xml[key] !== 'string') {
	                    throw new Error(`Expected string value for key ${String(key)}, got ${typeof xml[key]}`);
	                }
	                return xml[key];
	            }
	            if (specInstruction === 'number') {
	                return (0, xmlutil_1.xmlNum)(xml[key]);
	            }
	            if (specInstruction === 'boolean') {
	                return (0, xmlutil_1.xmlBool)(xml[key]);
	            }
	            if (specInstruction instanceof EnumArg) {
	                return specInstruction.FromXml(xml[key]);
	            }
	            if (typeof specInstruction === 'function') {
	                if (typeof specInstruction.FromXml === 'function') {
	                    return specInstruction.FromXml(xml[key], opts);
	                }
	                return specInstruction(xml, key);
	            }
	            throw new Error(`Unknown transform spec instruction: ${specInstruction}`);
	        };
	    }
	    const transformer = (rawTransformer instanceof OptionalArg ?
	        rawTransformer.type
	        : rawTransformer);
	    if (Array.isArray(transformer)) {
	        if (transformer.length !== 1) {
	            throw new Error(`Array transform spec must have exactly one element`);
	        }
	        return function transformArray(xml, key) {
	            const isOptional = rawTransformer instanceof OptionalArg;
	            if (!(key in xml) || xml[key] === undefined) {
	                if (isOptional) {
	                    return null;
	                }
	                throw new Error(`Missing required key ${String(key)}`);
	            }
	            return sigma_engine_shared_1.Utils.toArray(xml[key]).map((v, i, arr) => transformSpecInstruction(transformer[0])(arr, i));
	        };
	    }
	    return transformSpecInstruction(transformer);
	}
	function mergeDfn(obj) {
	    const { Defn, _attributes: _ignoredParentAttrs, ...rest } = obj;
	    if (Defn === undefined || Defn === null) {
	        return rest;
	    }
	    if (typeof Defn === 'object') {
	        for (const key of Object.keys(Defn)) {
	            if (key === '_attributes') {
	                continue;
	            }
	            if (key in rest) {
	                throw new Error(`Key ${key} exists in both Defn and top level and would overwrite it`);
	            }
	        }
	        return {
	            ...Defn,
	            ...rest,
	        };
	    }
	    return obj;
	}
	
	return xmlTransformer;
}

var portOccurrenceNormalization = {};

var hasRequiredPortOccurrenceNormalization;

function requirePortOccurrenceNormalization () {
	if (hasRequiredPortOccurrenceNormalization) return portOccurrenceNormalization;
	hasRequiredPortOccurrenceNormalization = 1;
	Object.defineProperty(portOccurrenceNormalization, "__esModule", { value: true });
	portOccurrenceNormalization.expandBusPortName = expandBusPortName;
	portOccurrenceNormalization.isPlausibleNetName = isPlausibleNetName;
	portOccurrenceNormalization.normalizePortOccurrences = normalizePortOccurrences;
	function expandBusPortName(busPortName) {
	    const match = busPortName.match(/^(.+)\[(\d+)(?:\.\.|:)(\d+)\]$/);
	    if (!match) {
	        return [];
	    }
	    const start = parseInt(match[2], 10);
	    const end = parseInt(match[3], 10);
	    const step = end >= start ? 1 : -1;
	    const result = [];
	    for (let index = start; step > 0 ? index <= end : index >= end; index += step) {
	        result.push({ name: `${match[1]}${index}`, index });
	    }
	    return result;
	}
	function isPlausibleNetName(value) {
	    return value !== undefined && /^[A-Za-z0-9_+.\-#:[\]/]+$/.test(value) && /[A-Za-z0-9]/.test(value);
	}
	function normalizedName(value) {
	    return String(value).toUpperCase();
	}
	function normalizePortOccurrences(portOccurrences) {
	    const bases = portOccurrences.filter((occurrence) => occurrence.RefBusMemberName === '');
	    const ownersByMemberName = new Map();
	    for (const base of bases) {
	        for (const member of expandBusPortName(String(base.RefPortName))) {
	            const key = normalizedName(member.name);
	            const owners = ownersByMemberName.get(key) ?? [];
	            owners.push(base);
	            ownersByMemberName.set(key, owners);
	        }
	    }
	    const diagnostics = [];
	    const normalized = portOccurrences.map((occurrence, ordinal) => {
	        if (occurrence.RefBusMemberName === '') {
	            return occurrence;
	        }
	        const owners = ownersByMemberName.get(normalizedName(occurrence.RefBusMemberName)) ?? [];
	        if (owners.length === 1) {
	            return { ...occurrence, RefKey: owners[0].RefKey };
	        }
	        if (owners.length > 1) {
	            diagnostics.push({
	                memberName: occurrence.RefBusMemberName,
	                candidateRefKeys: owners.map((owner) => String(owner.RefKey)),
	            });
	            return { ...occurrence, KeyType: 'AmbiguousPortMember', RefKey: `@orphan:${ordinal}` };
	        }
	        return occurrence;
	    });
	    return { portOccurrences: normalized, diagnostics };
	}
	
	return portOccurrenceNormalization;
}

var hasRequiredLogicalSchemaClasses;

function requireLogicalSchemaClasses () {
	if (hasRequiredLogicalSchemaClasses) return LogicalSchemaClasses;
	hasRequiredLogicalSchemaClasses = 1;
	Object.defineProperty(LogicalSchemaClasses, "__esModule", { value: true });
	LogicalSchemaClasses.OrCadDSNClasses = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const xmlutil_1 = requireXmlutil();
	const xmlTransformer_1 = requireXmlTransformer();
	const portOccurrenceNormalization_1 = requirePortOccurrenceNormalization();
	var OrCadDSNClasses;
	(function (OrCadDSNClasses) {
	    class Design {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Design((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Design._spec ??= {
	                DesignExportVersion: ['$DesignExportVersion', 'number'],
	                Name: ['$name', 'string'],
	                ObjectType: ['$objectType', new xmlTransformer_1.EnumArg(['SCHEMATIC'])],
	                RootName: ['$rootName', 'string'],
	                UseExplicitDbId: ['$useExplicitDbId', 'boolean'],
	                DateTime: ['DesignDateTime', DateTime],
	                DefaultValues: ['DefaultValues', DefaultValues],
	                Cache: ['Cache', Cache],
	                Netgroup: ['Netgroup', new xmlTransformer_1.OptionalArg(Netgroup)],
	                Schematic: ['Schematic', [Schematic]],
	                Occurrences: ['Occurrences', Occurrences],
	                Cis: ['CIS', CIS],
	                XmlnsXsd: ['$xmlns:xsd', new xmlTransformer_1.OptionalArg('string')],
	                XmlnsXsi: ['$xmlns:xsi', new xmlTransformer_1.OptionalArg('string')],
	                XsiNamespaceSchemaLocation: ['$xsi:noNamespaceSchemaLocation', new xmlTransformer_1.OptionalArg('string')],
	            })));
	        }
	    }
	    OrCadDSNClasses.Design = Design;
	    class Cache {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Cache((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Cache._spec ??= {
	                PortSymbol: ['PortSymbol', new xmlTransformer_1.OptionalArg([PortSymbol])],
	                ERCSymbol: ['ERCSymbol', new xmlTransformer_1.OptionalArg([ERCSymbol])],
	                OffPageSymbol: ['OffPageSymbol', new xmlTransformer_1.OptionalArg([OffPageSymbol])],
	                GlobalSymbol: ['GlobalSymbol', new xmlTransformer_1.OptionalArg([GlobalSymbol])],
	                TitleBlockSymbol: ['TitleBlockSymbol', new xmlTransformer_1.OptionalArg([TitleBlockSymbol])],
	                Package: ['Package', new xmlTransformer_1.OptionalArg([Package])],
	            })));
	        }
	    }
	    OrCadDSNClasses.Cache = Cache;
	    class DefaultPageRec {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DefaultPageRec((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DefaultPageRec._spec ??= {
	                ANSIGridRefs: ['$ANSIGridRefs', 'number'],
	                BorderDisplayed: ['$BorderDisplayed', 'number'],
	                BorderPrinted: ['$BorderPrinted', 'number'],
	                GridRefDisplayed: ['$GridRefDisplayed', 'number'],
	                GridRefPrinted: ['$GridRefPrinted', 'number'],
	                HorizontalLabelCount: ['$HorizontalLabelCount', 'number'],
	                HorizontalLabelIsAscending: ['$HorizontalLabelIsAscending', 'number'],
	                HorizontalLabelIsChar: ['$HorizontalLabelIsChar', 'number'],
	                HorizontalLabelWidth: ['$HorizontalLabelWidth', 'number'],
	                IsMetric: ['$IsMetric', 'number'],
	                PinToPin: ['$PinToPin', 'number'],
	                TitleBlockDisplayed: ['$TitleBlockDisplayed', 'number'],
	                TitleBlockPrinted: ['$TitleBlockPrinted', 'number'],
	                VerticalLabelCount: ['$VerticalLabelCount', 'number'],
	                VerticalLabelIsAscending: ['$VerticalLabelIsAscending', 'number'],
	                VerticalLabelIsChar: ['$VerticalLabelIsChar', 'number'],
	                VerticalLabelWidth: ['$VerticalLabelWidth', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.DefaultPageRec = DefaultPageRec;
	    class DefaultPartFieldMapping {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DefaultPartFieldMapping((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DefaultPartFieldMapping._spec ??= {
	                Index: ['$index', 'number'],
	                Value: ['$val', 'string'],
	            })));
	        }
	    }
	    OrCadDSNClasses.DefaultPartFieldMapping = DefaultPartFieldMapping;
	    class DefaultValues {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DefaultValues((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DefaultValues._spec ??= {
	                DefaultFont: ['DefaultFont', [Font]],
	                DefaultPageRec: ['DefaultPageRec', new xmlTransformer_1.OptionalArg(DefaultPageRec)],
	                DefaultPlacedInstIsPrimitive: [
	                    'DefaultPlacedInstIsPrimitive',
	                    new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlNum)(xml[key]?.Defn?.$val)),
	                ],
	                DefaultDrawnInstIsPrimitive: [
	                    'DefaultDrawnInstIsPrimitive',
	                    new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlNum)(xml[key]?.Defn?.$val)),
	                ],
	                DefaultPartFieldMappings: ['DefaultPartFieldMapping', new xmlTransformer_1.OptionalArg([DefaultPartFieldMapping])],
	            })));
	        }
	    }
	    OrCadDSNClasses.DefaultValues = DefaultValues;
	    class Font {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Font((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Font._spec ??= {
	                Charset: ['$charset', 'number'],
	                Escapement: ['$escapement', 'number'],
	                Index: ['$index', new xmlTransformer_1.OptionalArg('number')],
	                Height: ['$height', 'number'],
	                Italic: ['$italic', 'number'],
	                Name: ['$name', 'string'],
	                Orientation: ['$orientation', 'number'],
	                Weight: ['$weight', 'number'],
	                Width: ['$width', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Font = Font;
	    class Color {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Color((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Color._spec ??= {
	                Value: ['$val', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Color = Color;
	    class DisplayType {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DisplayType((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DisplayType._spec ??= {
	                ValueIfValueExist: ['$ValueIfValueExist', 'number'],
	                Value: ['$val', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.DisplayType = DisplayType;
	    class BoundingBox {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new BoundingBox((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (BoundingBox._spec ??= {
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.BoundingBox = BoundingBox;
	    class Line {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Line((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Line._spec ??= {
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Line = Line;
	    class SymbolDisplayProperty {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new SymbolDisplayProperty((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (SymbolDisplayProperty._spec ??= {
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                TextJustification: ['$textJustification', 'number'],
	                Font: ['PropFont', Font],
	                Color: ['PropColor', Color],
	                DisplayType: ['PropDispType', DisplayType],
	            })));
	        }
	    }
	    OrCadDSNClasses.SymbolDisplayProperty = SymbolDisplayProperty;
	    class SymbolPinScalar {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new SymbolPinScalar((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (SymbolPinScalar._spec ??= {
	                HotptX: ['$hotptX', 'number'],
	                HotptY: ['$hotptY', 'number'],
	                Name: ['$name', 'string'],
	                Position: ['$position', 'number'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                Type: ['$type', new xmlTransformer_1.OptionalArg('number')],
	                Visible: ['$visible', new xmlTransformer_1.OptionalArg('number')],
	                IsLong: ['IsLong', new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val))],
	                IsClock: ['IsClock', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsDot: ['IsDot', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsLeftPointing: ['IsLeftPointing', new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val))],
	                IsRightPointing: ['IsRightPointing', new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val))],
	                IsNetStyle: ['IsNetStyle', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsNoConnect: ['IsNoConnect', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsGlobal: ['IsGlobal', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsNumberVisible: ['IsNumberVisible', new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val))],
	                PinUserProperties: ['PinUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                PinDisplayProperties: ['PinDisplayProp', new xmlTransformer_1.OptionalArg([SymbolDisplayProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.SymbolPinScalar = SymbolPinScalar;
	    class Ellipse {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Ellipse((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Ellipse._spec ??= {
	                FillStyle: ['$fillStyle', 'number'],
	                HatchStyle: ['$hatchStyle', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Ellipse = Ellipse;
	    class ElementWithGeometries {
	        static TransformArgs() {
	            return {
	                Line: ['Line', new xmlTransformer_1.OptionalArg([Line])],
	                Rectangle: ['Rect', new xmlTransformer_1.OptionalArg([Rectangle])],
	                Ellipse: ['Ellipse', new xmlTransformer_1.OptionalArg([Ellipse])],
	                Polygon: ['Polygon', new xmlTransformer_1.OptionalArg([Polygon])],
	                Polyline: ['Polyline', new xmlTransformer_1.OptionalArg([Polyline])],
	                Arc: ['Arc', new xmlTransformer_1.OptionalArg([Arc])],
	                CommentText: ['CommentText', new xmlTransformer_1.OptionalArg([CommentText])],
	                Bitmap: ['Bitmap', new xmlTransformer_1.OptionalArg([Bitmap])],
	            };
	        }
	    }
	    class Polygon {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Polygon((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Polygon._spec ??= {
	                FillStyle: ['$fillStyle', 'number'],
	                HatchStyle: ['$hatchStyle', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                PolygonPoint: ['PolygonPoint', [XYPoint]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Polygon = Polygon;
	    class Polyline {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Polyline((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Polyline._spec ??= {
	                ArrowHeadSize: ['$arrowHeadSize', new xmlTransformer_1.OptionalArg('number')],
	                EndShape: ['$endShape', new xmlTransformer_1.OptionalArg('number')],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                PolylinePoint: ['PolylinePoint', [XYPoint]],
	                StartShape: ['$startShape', new xmlTransformer_1.OptionalArg('number')],
	            })));
	        }
	    }
	    OrCadDSNClasses.Polyline = Polyline;
	    class Arc {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Arc((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Arc._spec ??= {
	                EndX: ['$endX', 'number'],
	                EndY: ['$endY', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Arc = Arc;
	    class ERCSymbol extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new ERCSymbol((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (ERCSymbol._spec ??= {
	                Name: ['$name', 'string'],
	                SourceLibName: ['$sourceLibName', 'string'],
	                BoundingBox: ['SymbolBBox', BoundingBox],
	                ...ElementWithGeometries.TransformArgs(),
	            })));
	        }
	    }
	    OrCadDSNClasses.ERCSymbol = ERCSymbol;
	    class GlobalSymbol extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GlobalSymbol((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GlobalSymbol._spec ??= {
	                ...ElementWithGeometries.TransformArgs(),
	                Name: ['$name', 'string'],
	                SourceLibName: ['$sourceLibName', 'string'],
	                BoundingBox: ['SymbolBBox', BoundingBox],
	                PinScalar: ['SymbolPinScalar', [SymbolPinScalar]],
	                SymbolDisplayProperty: ['SymbolDisplayProp', new xmlTransformer_1.OptionalArg(SymbolDisplayProperty)],
	                Line: ['Line', [Line]],
	                SymbolUserProperties: ['SymbolUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.GlobalSymbol = GlobalSymbol;
	    class OffPageSymbol extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new OffPageSymbol((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (OffPageSymbol._spec ??= {
	                ...ElementWithGeometries.TransformArgs(),
	                Name: ['$name', 'string'],
	                SourceLibName: ['$sourceLibName', 'string'],
	                SymbolDisplayProperty: ['SymbolDisplayProp', new xmlTransformer_1.OptionalArg(SymbolDisplayProperty)],
	                BoundingBox: ['SymbolBBox', BoundingBox],
	                PinScalar: ['SymbolPinScalar', [SymbolPinScalar]],
	                SymbolUserProperties: ['SymbolUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.OffPageSymbol = OffPageSymbol;
	    class UserProperty {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new UserProperty((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (UserProperty._spec ??= {
	                Name: ['$name', 'string'],
	                Value: ['$val', 'string'],
	                UserEdit: ['$useredit', new xmlTransformer_1.OptionalArg('number')],
	            })));
	        }
	    }
	    OrCadDSNClasses.UserProperty = UserProperty;
	    class CommentText {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new CommentText((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (CommentText._spec ??= {
	                DbId: ['$dbId', new xmlTransformer_1.OptionalArg('number')],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                TextJustification: ['$textJustification', 'number'],
	                Rotation: ['$rotation', new xmlTransformer_1.OptionalArg('number')],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	                Font: ['TextFont', Font],
	                Color: ['$color', new xmlTransformer_1.OptionalArg('number')],
	            })));
	        }
	    }
	    OrCadDSNClasses.CommentText = CommentText;
	    class Rectangle {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Rectangle((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Rectangle._spec ??= {
	                FillStyle: ['$fillStyle', 'number'],
	                HatchStyle: ['$hatchStyle', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Rectangle = Rectangle;
	    class Bitmap {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Bitmap((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Bitmap._spec ??= {
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Value: ['$val', 'string'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Bitmap = Bitmap;
	    class TitleBlockSymbol extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new TitleBlockSymbol((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (TitleBlockSymbol._spec ??= {
	                ...ElementWithGeometries.TransformArgs(),
	                Name: ['$name', new xmlTransformer_1.OptionalArg('string')],
	                SourceLibName: ['$sourceLibName', new xmlTransformer_1.OptionalArg('string')],
	                SymbolDisplayProperty: ['SymbolDisplayProp', new xmlTransformer_1.OptionalArg([SymbolDisplayProperty])],
	                SymbolUserProperty: ['SymbolUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                BoundingBox: ['SymbolBBox', new xmlTransformer_1.OptionalArg(BoundingBox)],
	            })));
	        }
	    }
	    OrCadDSNClasses.TitleBlockSymbol = TitleBlockSymbol;
	    class PinNumber {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PinNumber((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PinNumber._spec ??= {
	                Number: ['$number', 'string'],
	                Position: ['$position', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.PinNumber = PinNumber;
	    class PhysicalPart {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PhysicalPart((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PhysicalPart._spec ??= {
	                PinNumber: ['PinNumber', new xmlTransformer_1.OptionalArg([PinNumber])],
	                PinSwap: ['PinSwap', new xmlTransformer_1.OptionalArg([PinSwap])],
	            })));
	        }
	    }
	    OrCadDSNClasses.PhysicalPart = PhysicalPart;
	    class PinSwap {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PinSwap((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PinSwap._spec ??= {
	                Position: ['$position', 'number'],
	                SwapId: ['$swapid', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.PinSwap = PinSwap;
	    class SymbolVector {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new SymbolVector((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (SymbolVector._spec ??= {
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Polyline: ['Polyline', Polyline],
	            })));
	        }
	    }
	    OrCadDSNClasses.SymbolVector = SymbolVector;
	    class NormalView extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new NormalView((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (NormalView._spec ??= {
	                ...ElementWithGeometries.TransformArgs(),
	                Suffix: ['$suffix', 'string'],
	                SymbolColor: ['SymbolColor', Color],
	                BoundingBox: ['SymbolBBox', BoundingBox],
	                IsPinNumbersVisible: [
	                    'IsPinNumbersVisible',
	                    new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val === undefined ? undefined : (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)),
	                ],
	                IsPinNamesRotated: [
	                    'IsPinNamesRotated',
	                    new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val === undefined ? undefined : (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)),
	                ],
	                IsPinNamesVisible: [
	                    'IsPinNamesVisible',
	                    new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val === undefined ? undefined : (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)),
	                ],
	                ContentsLibName: ['ContentsLibName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewName: ['ContentsViewName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewType: ['ContentsViewType', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$type)],
	                PartValue: ['PartValue', (xml, key) => xml[key]?.Defn?.$name],
	                Reference: ['Reference', (xml, key) => xml[key]?.Defn?.$name],
	                PinScalar: ['SymbolPinScalar', new xmlTransformer_1.OptionalArg([SymbolPinScalar])],
	                PinBus: ['SymbolPinBus', new xmlTransformer_1.OptionalArg([SymbolPinScalar])],
	                SymbolVector: ['SymbolVector', new xmlTransformer_1.OptionalArg([SymbolVector])],
	                SymbolDisplayProperty: ['SymbolDisplayProp', new xmlTransformer_1.OptionalArg([SymbolDisplayProperty])],
	                UserProperties: ['SymbolUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.NormalView = NormalView;
	    class ConvertView extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new ConvertView((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (ConvertView._spec ??= {
	                ...ElementWithGeometries.TransformArgs(),
	                Suffix: ['$suffix', 'string'],
	                SymbolColor: ['SymbolColor', Color],
	                BoundingBox: ['SymbolBBox', BoundingBox],
	                IsPinNumbersVisible: [
	                    'IsPinNumbersVisible',
	                    new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val === undefined ? undefined : (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)),
	                ],
	                IsPinNamesRotated: [
	                    'IsPinNamesRotated',
	                    new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val === undefined ? undefined : (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)),
	                ],
	                IsPinNamesVisible: [
	                    'IsPinNamesVisible',
	                    new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val === undefined ? undefined : (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)),
	                ],
	                ContentsLibName: ['ContentsLibName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewName: ['ContentsViewName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewType: ['ContentsViewType', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$type)],
	                PartValue: ['PartValue', (xml, key) => xml[key]?.Defn?.$name],
	                Reference: ['Reference', (xml, key) => xml[key]?.Defn?.$name],
	                PinScalar: ['SymbolPinScalar', new xmlTransformer_1.OptionalArg([SymbolPinScalar])],
	                PinBus: ['SymbolPinBus', new xmlTransformer_1.OptionalArg([SymbolPinScalar])],
	                SymbolVector: ['SymbolVector', new xmlTransformer_1.OptionalArg([SymbolVector])],
	                SymbolDisplayProperty: ['SymbolDisplayProp', new xmlTransformer_1.OptionalArg([SymbolDisplayProperty])],
	                UserProperties: ['SymbolUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.ConvertView = ConvertView;
	    class LibPart {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new LibPart((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (LibPart._spec ??= {
	                CellName: ['$CellName', new xmlTransformer_1.OptionalArg('string')],
	                NormalView: ['NormalView', new xmlTransformer_1.OptionalArg([NormalView])],
	                PhysicalPart: ['PhysicalPart', new xmlTransformer_1.OptionalArg([PhysicalPart])],
	                ConvertView: ['ConvertView', new xmlTransformer_1.OptionalArg([ConvertView])],
	            })));
	        }
	    }
	    OrCadDSNClasses.LibPart = LibPart;
	    class Package {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Package((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Package._spec ??= {
	                AlphabeticNumbering: ['$alphabeticNumbering', 'number'],
	                IsHomogeneous: ['$isHomogeneous', 'boolean'],
	                Name: ['$name', 'string'],
	                PcbFootprint: ['$pcbFootprint', 'string'],
	                PcbLib: ['$pcbLib', 'string'],
	                RefdesPrefix: ['$refdesPrefix', 'string'],
	                SourceLibName: ['$sourceLibName', 'string'],
	                LibPart: ['LibPart', [LibPart]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Package = Package;
	    class BundleMember {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new BundleMember((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (BundleMember._spec ??= {
	                Name: ['$name', 'string'],
	                Type: ['$type', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.BundleMember = BundleMember;
	    class Bundle {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Bundle((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Bundle._spec ??= {
	                Name: ['$name', 'string'],
	                Member: ['BundleMember', [BundleMember]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Bundle = Bundle;
	    class Netgroup {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Netgroup((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Netgroup._spec ??= {
	                Bundle: ['Bundle', [Bundle]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Netgroup = Netgroup;
	    class WireScalar {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new WireScalar((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (WireScalar._spec ??= {
	                DbId: ['$dbId', 'number'],
	                EndX: ['$endX', 'number'],
	                EndY: ['$endY', 'number'],
	                Name: ['$name', 'string'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                Color: ['WireColor', Color],
	                LineStyle: ['WireLineStyle', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                LineWidth: ['WireLineWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                BoundingBox: ['WireBBox', BoundingBox],
	                DisplayProperty: ['WireDisplayProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	                UserProperty: ['WireUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                Alias: ['Alias', new xmlTransformer_1.OptionalArg([Alias])],
	                Junction: ['Junction', new xmlTransformer_1.OptionalArg([Junction])],
	            })));
	        }
	    }
	    OrCadDSNClasses.WireScalar = WireScalar;
	    class Junction {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Junction((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Junction._spec ??= {
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.Junction = Junction;
	    class WireBus extends WireScalar {
	        constructor(from) {
	            super(from);
	        }
	        static FromXml(arg, opts) {
	            return new WireBus((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (WireBus._wireBusSpec ??= {
	                DbId: ['$dbId', 'number'],
	                EndX: ['$endX', 'number'],
	                EndY: ['$endY', 'number'],
	                Name: ['$name', 'string'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                Color: ['WireColor', Color],
	                LineStyle: ['WireLineStyle', 'number'],
	                LineWidth: ['WireLineWidth', 'number'],
	                BoundingBox: ['WireBBox', BoundingBox],
	                DisplayProperty: ['WireDisplayProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	                UserProperty: ['WireUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                Alias: ['Alias', new xmlTransformer_1.OptionalArg([Alias])],
	                Junction: ['Junction', new xmlTransformer_1.OptionalArg([Junction])],
	            })));
	        }
	    }
	    OrCadDSNClasses.WireBus = WireBus;
	    class Alias {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Alias((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Alias._spec ??= {
	                Color: ['$color', new xmlTransformer_1.OptionalArg('number')],
	                DbId: ['$dbId', new xmlTransformer_1.OptionalArg('number')],
	                LocX: ['$locX', new xmlTransformer_1.OptionalArg('number')],
	                LocY: ['$locY', new xmlTransformer_1.OptionalArg('number')],
	                Name: ['$name', new xmlTransformer_1.OptionalArg('string')],
	                Rotation: ['$rotation', new xmlTransformer_1.OptionalArg('number')],
	                BoundingBox: ['AliasBBox', new xmlTransformer_1.OptionalArg(BoundingBox)],
	                Font: ['AliasFont', new xmlTransformer_1.OptionalArg(Font)],
	                BundleOwner: ['BundleOwner', new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$val)],
	            }), true));
	        }
	    }
	    OrCadDSNClasses.Alias = Alias;
	    class NetScalar {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new NetScalar((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (NetScalar._spec ??= {
	                NetColor: ['$NetColor', 'number'],
	                NetLineStyle: ['$NetLineStyle', 'number'],
	                NetLineWidth: ['$NetLineWidth', 'number'],
	                Name: ['$name', 'string'],
	                WireScalars: ['WireScalar', new xmlTransformer_1.OptionalArg([WireScalar])],
	            })));
	        }
	    }
	    OrCadDSNClasses.NetScalar = NetScalar;
	    class NetBus {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new NetBus((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (NetBus._spec ??= {
	                NetColor: ['$NetColor', 'number'],
	                NetLineStyle: ['$NetLineStyle', 'number'],
	                NetLineWidth: ['$NetLineWidth', 'number'],
	                Name: ['$name', 'string'],
	                WireBus: ['WireBus', new xmlTransformer_1.OptionalArg([WireScalar])],
	                NetScalar: ['NetScalar', new xmlTransformer_1.OptionalArg([NetScalar])],
	            })));
	        }
	    }
	    OrCadDSNClasses.NetBus = NetBus;
	    class GlobalDisplayProperty {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GlobalDisplayProperty((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GlobalDisplayProperty._spec ??= {
	                DbId: ['$dbId', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                TextJustification: ['$textJustification', 'number'],
	                Font: ['PropFont', Font],
	                Color: ['PropColor', Color],
	                DisplayType: ['PropDispType', DisplayType],
	            })));
	        }
	    }
	    OrCadDSNClasses.GlobalDisplayProperty = GlobalDisplayProperty;
	    class Global {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Global((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Global._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                LibName: ['$libName', 'string'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Mirror: ['$mirror', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                SymbolName: ['$symbolName', 'string'],
	                DisplayProperty: ['GlobalDisplayProp', new xmlTransformer_1.OptionalArg(GlobalDisplayProperty)],
	                UserProperty: ['GlobalUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.Global = Global;
	    class TitleBlockDisplayProperty {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new TitleBlockDisplayProperty((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (TitleBlockDisplayProperty._spec ??= {
	                DbId: ['$dbId', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                TextJustification: ['$textJustification', 'number'],
	                Font: ['PropFont', Font],
	                Color: ['PropColor', Color],
	                DisplayType: ['PropDispType', DisplayType],
	            })));
	        }
	    }
	    OrCadDSNClasses.TitleBlockDisplayProperty = TitleBlockDisplayProperty;
	    class TitleBlock {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new TitleBlock((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (TitleBlock._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                LibName: ['$libName', 'string'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                SymbolName: ['$symbolName', 'string'],
	                KeepInLowerRightCorner: ['KeepInLowerRightCorner', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                PageCount: ['PageCount', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$count)],
	                PageNumber: ['PageNumber', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                DisplayProperty: ['TitleBlockDisplayProp', new xmlTransformer_1.OptionalArg([TitleBlockDisplayProperty])],
	                UserProperty: ['TitleBlockUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.TitleBlock = TitleBlock;
	    class PartInstDisplayProperty {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PartInstDisplayProperty((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PartInstDisplayProperty._spec ??= {
	                DbId: ['$dbId', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                TextJustification: ['$textJustification', 'number'],
	                UserEdit: ['$useredit', new xmlTransformer_1.OptionalArg((xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val))],
	                Font: ['PropFont', Font],
	                Color: ['PropColor', Color],
	                DisplayType: ['PropDispType', DisplayType],
	            })));
	        }
	    }
	    OrCadDSNClasses.PartInstDisplayProperty = PartInstDisplayProperty;
	    class PortInstScalar {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            const { $RecordedX, $RecordedY, $RecordedWireDbId, $recordedX, $recordedY, $recordedWireDbId, ...xmlDefn } = arg.Defn;
	            const xmlArg = { ...arg, Defn: xmlDefn };
	            return new PortInstScalar((0, xmlTransformer_1.transformXmlArgs)(xmlArg, opts, (PortInstScalar._spec ??= {
	                IsPlacedInstPin: ['$IsPlacedInstPin', 'boolean'],
	                DbId: ['$dbId', 'number'],
	                HotptX: ['$hotptX', 'number'],
	                HotptY: ['$hotptY', 'number'],
	                Name: ['$name', 'string'],
	                Position: ['$position', 'number'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                Type: ['$type', 'number'],
	                Visible: ['$visible', 'boolean'],
	                IsNoConnect: ['IsNoConnect', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                UserProperty: ['PortInstUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                DisplayProperty: ['PortInstDisplayProp', new xmlTransformer_1.OptionalArg([PartInstDisplayProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.PortInstScalar = PortInstScalar;
	    class PartInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PartInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PartInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                DeviceDesignator: ['$deviceDesignator', 'string'],
	                LibName: ['$libName', 'string'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Mirror: ['$mirror', 'number'],
	                Name: ['$name', 'string'],
	                PkgName: ['$pkgName', 'string'],
	                PowerPinsVisible: ['$powerpinsvisible', 'boolean'],
	                Rotation: ['$rotation', 'number'],
	                GraphicName: ['GraphicName', (xml, key) => xml[key]?.Defn?.$name],
	                DisplayProperty: ['PartInstDisplayProp', new xmlTransformer_1.OptionalArg([PartInstDisplayProperty])],
	                UserProperty: ['PartInstUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                ContentsLibName: ['ContentsLibName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewName: ['ContentsViewName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewType: ['ContentsViewType', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$type)],
	                IsPrimitiveProp: ['IsPrimitiveProp', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$type)],
	                PartValue: ['PartValue', (xml, key) => String(xml[key]?.Defn?.$name)],
	                Reference: ['Reference', (xml, key) => xml[key]?.Defn?.$name],
	                PortInstScalar: ['PortInstScalar', new xmlTransformer_1.OptionalArg([PortInstScalar])],
	                PortInstBus: ['PortInstBus', new xmlTransformer_1.OptionalArg([PortInstScalar])],
	                PCBLib: ['PCBLib', new xmlTransformer_1.OptionalArg((xml, key) => xml[key]?.Defn?.$name)],
	            })));
	        }
	    }
	    OrCadDSNClasses.PartInst = PartInst;
	    class Schematic {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Schematic((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Schematic._spec ??= {
	                Name: ['$name', 'string'],
	                MaxDbId: ['MaxDbId', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                DateTime: ['SchematicDateTime', DateTime],
	                Page: ['Page', [SchematicPage]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Schematic = Schematic;
	    class PageSizeInfo {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PageSizeInfo((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PageSizeInfo._spec ??= {
	                IsMetric: ['$IsMetric', 'boolean'],
	                PinToPin: ['$PinToPin', 'number'],
	                Height: ['$height', 'number'],
	                Width: ['$width', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.PageSizeInfo = PageSizeInfo;
	    class XYPoint {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new XYPoint((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (XYPoint._spec ??= {
	                X: ['$x', 'number'],
	                Y: ['$y', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.XYPoint = XYPoint;
	    class DateTime {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            if (!arg.Defn) {
	                throw new Error(`No Defn property found in DateTime`);
	            }
	            return new DateTime((0, xmlTransformer_1.transformXmlArgs)(arg, opts, {
	                CreateDate: [DateTime.determineKeyPrefix(arg.Defn, '$createDate'), 'string'],
	                CreateDateEpoch: [DateTime.determineKeyPrefix(arg.Defn, '$createDateEpoch'), 'number'],
	                CreateTime: [this.determineKeyPrefix(arg.Defn, '$createTime'), 'string'],
	                ModifyDate: [this.determineKeyPrefix(arg.Defn, '$modifyDate'), 'string'],
	                ModifyDateEpoch: [this.determineKeyPrefix(arg.Defn, '$modifyDateEpoch'), 'number'],
	                ModifyTime: [this.determineKeyPrefix(arg.Defn, '$modifyTime'), 'string'],
	            }));
	        }
	        static determineKeyPrefix(arg, k) {
	            const stripped = k.substring(1);
	            const key = Object.keys(arg).find((k) => k.endsWith(stripped));
	            if (!key) {
	                throw new Error(`Could not find key for ${k} in ${JSON.stringify(arg)}`);
	            }
	            return key;
	        }
	    }
	    OrCadDSNClasses.DateTime = DateTime;
	    class SchematicPage {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new SchematicPage((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (SchematicPage._spec ??= {
	                Name: ['$name', 'string'],
	                TitleBlockDisplayed: ['TitleblockDisplayed', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                BorderDisplayed: ['BorderDisplayed', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                GridRefDisplayed: ['GridRefDisplayed', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                PageSizeInfo: ['PageSizeInfo', PageSizeInfo],
	                PageSize: ['PageSize', (xml, key) => xml[key]?.Defn?.$val],
	                OuterBorderMargin: ['OuterBorderMagin', XYPoint],
	                OuterLineStyle: ['OuterLineStyle', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                OuterLineWidth: ['OuterLineWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                OuterBorderIsVisible: ['OuterBorderIsVisible', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                InnerBorderMargin: ['InnerBorderMargin', XYPoint],
	                InnerLineStyle: ['InnerLineStyle', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                InnerLineWidth: ['InnerLineWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                InnerLineVisible: ['InnerLineVisible', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                Color: ['Color', Color],
	                Font: ['Font', Font],
	                LabelColor: ['LabelColor', Color],
	                HorizontalCount: ['HorizontalCount', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$count)],
	                HorizontalWidth: ['HorizontalWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                HorizontalChar: ['HorizontalChar', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                HorizontalVisible: ['HorizontalVisible', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                HorizontalAscending: ['HorizontalAscending', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                HorizontalStyle: ['HorizontalStyle', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                HorizontalSeparatorWidth: ['HorizontalSeparatorWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                VerticalCount: ['VerticalCount', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$count)],
	                VerticalWidth: ['VerticalWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                VerticalChar: ['VerticalChar', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                VerticalVisible: ['VerticalVisible', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                VerticalAscending: ['VerticalAscending', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                VerticalStyle: ['VerticalStyle', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                VerticalSeparatorWidth: ['VerticalSeparatorWidth', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$val)],
	                PageDateTime: ['PageDateTime', DateTime],
	                TitleBlock: ['TitleBlock', new xmlTransformer_1.OptionalArg([TitleBlock])],
	                NetScalar: ['NetScalar', new xmlTransformer_1.OptionalArg([NetScalar])],
	                NetBus: ['NetBus', new xmlTransformer_1.OptionalArg([NetBus])],
	                Global: ['Global', new xmlTransformer_1.OptionalArg([Global])],
	                PartInst: ['PartInst', new xmlTransformer_1.OptionalArg([PartInst])],
	                DrawnInst: ['DrawnInst', new xmlTransformer_1.OptionalArg([DrawnInst])],
	                GraphicCommentTextInst: ['GraphicCommentTextInst', new xmlTransformer_1.OptionalArg([CommentText])],
	                GraphicLineInst: ['GraphicLineInst', new xmlTransformer_1.OptionalArg([GraphicLineInst])],
	                GraphicBitMapInst: ['GraphicBitMapInst', new xmlTransformer_1.OptionalArg([GraphicBitMapInst])],
	                GraphicOleEmbedInst: ['GraphicOleEmbedInst', new xmlTransformer_1.OptionalArg([GraphicOleEmbedInst])],
	                Port: ['Port', new xmlTransformer_1.OptionalArg([Port])],
	                BusEntry: ['BusEntry', new xmlTransformer_1.OptionalArg([BusEntry])],
	                OffPageConnector: ['OffPageConnector', new xmlTransformer_1.OptionalArg([OffPageConnector])],
	                GraphicBoxInst: ['GraphicBoxInst', new xmlTransformer_1.OptionalArg([GraphicBoxInst])],
	                GraphicPolygonInst: ['GraphicPolygonInst', new xmlTransformer_1.OptionalArg([GraphicPolygonInst])],
	                GraphicPolylineInst: ['GraphicPolylineInst', new xmlTransformer_1.OptionalArg([GraphicPolylineInst])],
	                GraphicEllipseInst: ['GraphicEllipseInst', new xmlTransformer_1.OptionalArg([GraphicEllipseInst])],
	                ERC: ['ERC', new xmlTransformer_1.OptionalArg(ERC)],
	            })));
	        }
	    }
	    OrCadDSNClasses.SchematicPage = SchematicPage;
	    class ERC {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new ERC({
	                something: 'something',
	            });
	        }
	    }
	    OrCadDSNClasses.ERC = ERC;
	    class GraphicPolygonInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicPolygonInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicPolygonInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                FillStyle: ['$fillStyle', 'number'],
	                HatchStyle: ['$hatchStyle', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                PolygonPoint: ['PolygonPoint', [XYPoint]],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicPolygonInst = GraphicPolygonInst;
	    class GraphicPolylineInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicPolylineInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicPolylineInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                PolylinePoint: ['PolylinePoint', [XYPoint]],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicPolylineInst = GraphicPolylineInst;
	    class GraphicEllipseInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicEllipseInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicEllipseInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                FillStyle: ['$fillStyle', 'number'],
	                HatchStyle: ['$hatchStyle', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicEllipseInst = GraphicEllipseInst;
	    class GraphicBoxInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicBoxInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicBoxInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                FillStyle: ['$fillStyle', 'number'],
	                HatchStyle: ['$hatchStyle', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicBoxInst = GraphicBoxInst;
	    class OffPageConnector {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new OffPageConnector((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (OffPageConnector._spec ??= {
	                Color: ['$color', new xmlTransformer_1.OptionalArg('number')],
	                DbId: ['$dbId', 'number'],
	                LibName: ['$libName', new xmlTransformer_1.OptionalArg('string')],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Mirror: ['$mirror', new xmlTransformer_1.OptionalArg('number')],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                SymbolName: ['$symbolName', new xmlTransformer_1.OptionalArg('string')],
	                Type: ['$type', new xmlTransformer_1.OptionalArg('number')],
	                TextJustification: ['$textJustification', new xmlTransformer_1.OptionalArg('number')],
	                Useredit: ['$useredit', new xmlTransformer_1.OptionalArg('number')],
	                OffPageConnectorDisplayProp: ['OffPageConnectorDisplayProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	                OffPageConnectorUserProp: ['OffPageConnectorUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.OffPageConnector = OffPageConnector;
	    class BusEntry {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new BusEntry((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (BusEntry._spec ??= {
	                BusX: ['$busX', 'number'],
	                BusY: ['$busY', 'number'],
	                EndX: ['$endX', 'number'],
	                EndY: ['$endY', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.BusEntry = BusEntry;
	    class Port {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Port((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Port._spec ??= {
	                Color: ['$color', new xmlTransformer_1.OptionalArg('number')],
	                DbId: ['$dbId', 'number'],
	                LibName: ['$libName', new xmlTransformer_1.OptionalArg('string')],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Mirror: ['$mirror', new xmlTransformer_1.OptionalArg('number')],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', new xmlTransformer_1.OptionalArg('number')],
	                SymbolName: ['$symbolName', new xmlTransformer_1.OptionalArg('string')],
	                Type: ['$type', new xmlTransformer_1.OptionalArg('number')],
	                TextJustification: ['$textJustification', new xmlTransformer_1.OptionalArg('number')],
	                Useredit: ['$useredit', new xmlTransformer_1.OptionalArg('number')],
	                PortDisplayProp: ['PortDisplayProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	                PortUserProp: ['PortUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.Port = Port;
	    class DrawnInstPortScalar {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DrawnInstPortScalar((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DrawnInstPortScalar._spec ??= {
	                DbId: ['$dbId', new xmlTransformer_1.OptionalArg('number')],
	                HotptX: ['$hotptX', 'number'],
	                HotptY: ['$hotptY', 'number'],
	                Name: ['$name', 'string'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                Type: ['$type', 'number'],
	                Visible: ['$visible', 'boolean'],
	                IsLong: ['IsLong', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsClock: ['IsClock', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsDot: ['IsDot', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsLeftPointing: ['IsLeftPointing', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsRightPointing: ['IsRightPointing', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsNetStyle: ['IsNetStyle', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsGlobal: ['IsGlobal', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsNoConnect: ['IsNoConnect', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	            })));
	        }
	    }
	    OrCadDSNClasses.DrawnInstPortScalar = DrawnInstPortScalar;
	    class DrawnInstPortBus {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DrawnInstPortBus((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DrawnInstPortBus._spec ??= {
	                DbId: ['$dbId', new xmlTransformer_1.OptionalArg('number')],
	                HotptX: ['$hotptX', 'number'],
	                HotptY: ['$hotptY', 'number'],
	                Name: ['$name', 'string'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	                Type: ['$type', 'number'],
	                Visible: ['$visible', 'boolean'],
	                UserProperties: ['PortInstUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                IsLong: ['IsLong', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsClock: ['IsClock', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsDot: ['IsDot', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsLeftPointing: ['IsLeftPointing', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsRightPointing: ['IsRightPointing', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsNetStyle: ['IsNetStyle', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsGlobal: ['IsGlobal', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	                IsNoConnect: ['IsNoConnect', (xml, key) => (0, xmlutil_1.xmlBool)(xml[key]?.Defn?.$val)],
	            })));
	        }
	    }
	    OrCadDSNClasses.DrawnInstPortBus = DrawnInstPortBus;
	    class DrawnInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new DrawnInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (DrawnInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                DeviceDesignator: ['$deviceDesignator', 'string'],
	                LibName: ['$libName', 'string'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Mirror: ['$mirror', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	                DisplayProperty: ['DrawnInstDisplayProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	                UserProperty: ['DrawnInstUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                ContentsLibName: ['ContentsLibName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewName: ['ContentsViewName', (xml, key) => xml[key]?.Defn?.$name],
	                ContentsViewType: ['ContentsViewType', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$type)],
	                IsPrimitiveProp: ['IsPrimitiveProp', (xml, key) => (0, xmlutil_1.xmlInt)(xml[key]?.Defn?.$type)],
	                PartValue: ['PartValue', (xml, key) => String(xml[key]?.Defn?.$name)],
	                Reference: ['Reference', (xml, key) => xml[key]?.Defn?.$name],
	                PortInstScalar: ['PortInstScalar', new xmlTransformer_1.OptionalArg([DrawnInstPortScalar])],
	                PortInstBus: ['PortInstBus', new xmlTransformer_1.OptionalArg([DrawnInstPortBus])],
	            })));
	        }
	    }
	    OrCadDSNClasses.DrawnInst = DrawnInst;
	    class GraphicBitMapInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicBitMapInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicBitMapInst._spec ??= {
	                DbId: ['$dbId', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Value: ['$val', 'string'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicBitMapInst = GraphicBitMapInst;
	    class GraphicOleEmbedInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicOleEmbedInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicOleEmbedInst._spec ??= {
	                DbId: ['$dbId', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Dib: ['$dib', 'string'],
	                DibSize: ['$dibSize', 'number'],
	                Value: ['$val', 'string'],
	                X1: ['$x1', 'number'],
	                X2: ['$x2', 'number'],
	                Y1: ['$y1', 'number'],
	                Y2: ['$y2', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicOleEmbedInst = GraphicOleEmbedInst;
	    class GraphicLineInst {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GraphicLineInst((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GraphicLineInst._spec ??= {
	                Color: ['$color', 'number'],
	                DbId: ['$dbId', 'number'],
	                EndX: ['$endX', 'number'],
	                EndY: ['$endY', 'number'],
	                LineStyle: ['$lineStyle', 'number'],
	                LineWidth: ['$lineWidth', 'number'],
	                LocX: ['$locX', 'number'],
	                LocY: ['$locY', 'number'],
	                Name: ['$name', 'string'],
	                Rotation: ['$rotation', 'number'],
	                StartX: ['$startX', 'number'],
	                StartY: ['$startY', 'number'],
	            })));
	        }
	    }
	    OrCadDSNClasses.GraphicLineInst = GraphicLineInst;
	    class CorePart {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new CorePart((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (CorePart._spec ??= {
	                OccId: ['$OccId', 'number'],
	                PartNumber: ['$PartNumber', 'string'],
	                Reference: ['$Reference', 'string'],
	                PartPresent: ['$PartPresent', new xmlTransformer_1.OptionalArg('string')],
	            })));
	        }
	    }
	    OrCadDSNClasses.CorePart = CorePart;
	    class Common {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Common((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Common._spec ??= {
	                CommonCorePart: ['CommonCorePart', [CorePart]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Common = Common;
	    class LinkedPart {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new LinkedPart((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (LinkedPart._spec ??= {
	                Definition: ['Defn', new xmlTransformer_1.OptionalArg('string')],
	                Property: ['PropNVP', new xmlTransformer_1.OptionalArg([NameValuePair])],
	            })));
	        }
	    }
	    OrCadDSNClasses.LinkedPart = LinkedPart;
	    class NameValuePair {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new NameValuePair((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (NameValuePair._spec ??= {
	                Name: ['$name', 'string'],
	                Value: ['$value', 'string'],
	            })));
	        }
	    }
	    OrCadDSNClasses.NameValuePair = NameValuePair;
	    class SubGroupName {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new SubGroupName((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (SubGroupName._spec ??= {
	                Name: ['$name', 'string'],
	                CorePart: ['GroupCorePart', new xmlTransformer_1.OptionalArg([CorePart])],
	                LinkedPart: ['LinkedPart', new xmlTransformer_1.OptionalArg([LinkedPart])],
	            })));
	        }
	    }
	    OrCadDSNClasses.SubGroupName = SubGroupName;
	    class GroupName {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new GroupName((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (GroupName._spec ??= {
	                Name: ['$name', 'string'],
	                SubGroupName: ['SubGroupName', [SubGroupName]],
	            })));
	        }
	    }
	    OrCadDSNClasses.GroupName = GroupName;
	    class Groups {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Groups((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Groups._spec ??= {
	                Common: ['Common', new xmlTransformer_1.OptionalArg(Common)],
	                GroupName: ['GroupName', new xmlTransformer_1.OptionalArg([GroupName])],
	            })));
	        }
	    }
	    OrCadDSNClasses.Groups = Groups;
	    class VariantEntry {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new VariantEntry((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (VariantEntry._spec ??= {
	                BomEntry: ['$BOMEntry', 'string'],
	            })));
	        }
	    }
	    OrCadDSNClasses.VariantEntry = VariantEntry;
	    class VariantName {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new VariantName((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (VariantName._spec ??= {
	                BomName: ['$BOMName', 'string'],
	                VariantEntry: ['VariantEntry', [VariantEntry]],
	            })));
	        }
	    }
	    OrCadDSNClasses.VariantName = VariantName;
	    class BOMVariants {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new BOMVariants((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (BOMVariants._spec ??= {
	                VariantName: ['VariantName', new xmlTransformer_1.OptionalArg([VariantName])],
	            })));
	        }
	    }
	    OrCadDSNClasses.BOMVariants = BOMVariants;
	    class PortOccurrence {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PortOccurrence((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PortOccurrence._spec ??= {
	                KeyType: ['$keyType', 'string'],
	                RefBusMemberName: ['$refBusMemberName', 'string'],
	                RefKey: ['$refKey', 'string'],
	                RefPortName: ['$refPortName', 'string'],
	                UserProperty: ['OccUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                DisplayProperty: ['OccDispProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.PortOccurrence = PortOccurrence;
	    class TitleBlockOccurrence {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new TitleBlockOccurrence((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (TitleBlockOccurrence._spec ??= {
	                RefInstId: ['$refInstId', new xmlTransformer_1.OptionalArg('string')],
	                UserProperty: ['OccUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                DisplayProperty: ['OccDispProp', new xmlTransformer_1.OptionalArg([TitleBlockDisplayProperty])],
	            })));
	        }
	    }
	    OrCadDSNClasses.TitleBlockOccurrence = TitleBlockOccurrence;
	    class InstOccurrence {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            const occurrence = new InstOccurrence((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (InstOccurrence._spec ??= {
	                RefInstId: ['$refInstId', 'string'],
	                UserProperty: ['OccUserProp', new xmlTransformer_1.OptionalArg([UserProperty])],
	                DisplayProperty: ['OccDispProp', new xmlTransformer_1.OptionalArg([GlobalDisplayProperty])],
	                PortOccurrence: ['PortOccurrence', new xmlTransformer_1.OptionalArg([PortOccurrence])],
	                TitleBlockOccurrence: ['TitleBlockOccurrence', new xmlTransformer_1.OptionalArg([TitleBlockOccurrence])],
	                InstOccurrence: ['InstOccurrence', new xmlTransformer_1.OptionalArg([InstOccurrence])],
	            })));
	            const normalized = (0, portOccurrenceNormalization_1.normalizePortOccurrences)(occurrence.PortOccurrence ?? []);
	            occurrence.PortOccurrence = normalized.portOccurrences.map((row) => new PortOccurrence(row));
	            for (const diagnostic of normalized.diagnostics) {
	                opts.processingResults.innerResults.push({
	                    type: 'orcad-port-occurrence',
	                    message: `[port-occurrence-ambiguous] refInstId=${occurrence.RefInstId} member=${diagnostic.memberName} candidates=${diagnostic.candidateRefKeys.join(',')}`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            return occurrence;
	        }
	    }
	    OrCadDSNClasses.InstOccurrence = InstOccurrence;
	    class Occurrences {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new Occurrences((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (Occurrences._spec ??= {
	                InstOccurrence: ['InstOccurrence', [InstOccurrence]],
	            })));
	        }
	    }
	    OrCadDSNClasses.Occurrences = Occurrences;
	    class CIS {
	        constructor(from) {
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new CIS((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (CIS._spec ??= {
	                Definition: ['Defn', new xmlTransformer_1.OptionalArg('string')],
	                Groups: ['Groups', Groups],
	                BomVariants: ['BOMVariants', BOMVariants],
	            })));
	        }
	    }
	    OrCadDSNClasses.CIS = CIS;
	    class PortSymbol extends ElementWithGeometries {
	        constructor(from) {
	            super();
	            (0, xmlTransformer_1.maybeApplyProps)(this, from);
	        }
	        static FromXml(arg, opts) {
	            return new PortSymbol((0, xmlTransformer_1.transformXmlArgs)(arg, opts, (PortSymbol._spec ??= {
	                ...ElementWithGeometries.TransformArgs(),
	                Name: ['$name', 'string'],
	                SourceLibName: ['$sourceLibName', 'string'],
	                SymbolDisplayProperty: ['SymbolDisplayProp', new xmlTransformer_1.OptionalArg(SymbolDisplayProperty)],
	                BoundingBox: ['SymbolBBox', BoundingBox],
	                Line: ['Line', new xmlTransformer_1.OptionalArg([Line])],
	                PinScalar: ['SymbolPinScalar', new xmlTransformer_1.OptionalArg(SymbolPinScalar)],
	            })));
	        }
	    }
	    OrCadDSNClasses.PortSymbol = PortSymbol;
	})(OrCadDSNClasses || (LogicalSchemaClasses.OrCadDSNClasses = OrCadDSNClasses = {}));
	
	return LogicalSchemaClasses;
}

var dsncfb = {};

var DsnCfbBinarySpec = {};

var hasRequiredDsnCfbBinarySpec;

function requireDsnCfbBinarySpec () {
	if (hasRequiredDsnCfbBinarySpec) return DsnCfbBinarySpec;
	hasRequiredDsnCfbBinarySpec = 1;
	Object.defineProperty(DsnCfbBinarySpec, "__esModule", { value: true });
	DsnCfbBinarySpec.DsnCfbBinarySpec = void 0;
	var DsnCfbBinarySpec$1;
	(function (DsnCfbBinarySpec) {
	    function readUInt16LE(bytes, offset) {
	        return bytes[offset] | (bytes[offset + 1] << 8);
	    }
	    DsnCfbBinarySpec.readUInt16LE = readUInt16LE;
	    function readInt16LE(bytes, offset) {
	        const value = readUInt16LE(bytes, offset);
	        return value >= 0x8000 ? value - 0x10000 : value;
	    }
	    DsnCfbBinarySpec.readInt16LE = readInt16LE;
	    function readUInt32LE(bytes, offset) {
	        return ((bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] * 0x1000000)) >>> 0);
	    }
	    DsnCfbBinarySpec.readUInt32LE = readUInt32LE;
	    function readInt32LE(bytes, offset) {
	        const value = readUInt32LE(bytes, offset);
	        return value >= 0x80000000 ? value - 0x100000000 : value;
	    }
	    DsnCfbBinarySpec.readInt32LE = readInt32LE;
	    function bytesToAscii(bytes, start, end) {
	        let value = '';
	        for (let i = start; i < end; i++) {
	            value += String.fromCharCode(bytes[i]);
	        }
	        return value;
	    }
	    DsnCfbBinarySpec.bytesToAscii = bytesToAscii;
	    function bytesToHex(bytes, start, end) {
	        let value = '';
	        for (let offset = start; offset < end; offset++) {
	            value += bytes[offset].toString(16).padStart(2, '0');
	        }
	        return value;
	    }
	    DsnCfbBinarySpec.bytesToHex = bytesToHex;
	    function isPrintableAsciiByte(byte) {
	        return byte >= 0x20 && byte < 0x7f;
	    }
	    DsnCfbBinarySpec.isPrintableAsciiByte = isPrintableAsciiByte;
	    function hasBytes(bytes, offset, expected) {
	        if (offset < 0 || offset + expected.length > bytes.length) {
	            return false;
	        }
	        return expected.every((byte, index) => bytes[offset + index] === byte);
	    }
	    DsnCfbBinarySpec.hasBytes = hasBytes;
	    function resolveOffset(spec, ctx) {
	        const offset = typeof spec === 'function' ? spec(ctx) : spec;
	        return offset === undefined ? ctx.offset : ctx.offset + offset;
	    }
	    function resolveLength(spec, ctx) {
	        return typeof spec === 'function' ? spec(ctx) : spec;
	    }
	    function fieldDiagnostic(ctx, specName, code, message, offset, byteLength) {
	        return {
	            severity: 'warning',
	            code,
	            message,
	            specName,
	            streamPath: ctx.streamPath,
	            ...(offset !== undefined ? { offset } : {}),
	            ...(byteLength !== undefined ? { byteLength } : {}),
	        };
	    }
	    function readCountedAscii(bytes, offset, maxLength, requireNullTerminator) {
	        if (offset + 2 > bytes.length) {
	            return undefined;
	        }
	        const length = readUInt16LE(bytes, offset);
	        const valueStart = offset + 2;
	        const valueEnd = valueStart + length;
	        if (length < 1 || length > maxLength || valueEnd > bytes.length) {
	            return undefined;
	        }
	        for (let index = valueStart; index < valueEnd; index++) {
	            if (!isPrintableAsciiByte(bytes[index])) {
	                return undefined;
	            }
	        }
	        const hasNullTerminator = valueEnd < bytes.length && bytes[valueEnd] === 0;
	        if (requireNullTerminator && !hasNullTerminator) {
	            return undefined;
	        }
	        return {
	            value: bytesToAscii(bytes, valueStart, valueEnd),
	            byteLength: 2 + length + (hasNullTerminator ? 1 : 0),
	        };
	    }
	    DsnCfbBinarySpec.readCountedAscii = readCountedAscii;
	    function readAsciiRuns(bytes, minLength, maxCount) {
	        const runs = [];
	        let start = -1;
	        for (let offset = 0; offset <= bytes.length; offset++) {
	            const byte = offset < bytes.length ? bytes[offset] : 0;
	            if (offset < bytes.length && isPrintableAsciiByte(byte)) {
	                if (start === -1) {
	                    start = offset;
	                }
	                continue;
	            }
	            if (start !== -1) {
	                const length = offset - start;
	                if (length >= minLength) {
	                    runs.push({ offset: start, length, value: bytesToAscii(bytes, start, offset) });
	                    if (runs.length >= maxCount) {
	                        return runs;
	                    }
	                }
	                start = -1;
	            }
	        }
	        return runs;
	    }
	    DsnCfbBinarySpec.readAsciiRuns = readAsciiRuns;
	    function readField(name, field, ctx, specName) {
	        if (field.when && !field.when(ctx)) {
	            return undefined;
	        }
	        const diagnostics = [];
	        const offset = resolveOffset(field.offset, ctx);
	        if (offset === undefined || offset < 0 || offset > ctx.bytes.length) {
	            if (field.optional) {
	                return undefined;
	            }
	            diagnostics.push(fieldDiagnostic(ctx, specName, 'field-offset-out-of-range', `Field ${name} offset is outside the payload`, offset));
	            return { value: undefined, ranges: [], diagnostics };
	        }
	        const requireBytes = (byteLength) => {
	            if (offset + byteLength <= ctx.bytes.length) {
	                return true;
	            }
	            diagnostics.push(fieldDiagnostic(ctx, specName, 'field-out-of-range', `Field ${name} extends past the payload`, offset, byteLength));
	            return false;
	        };
	        const requiredFieldFailure = () => (field.optional ? undefined : { value: undefined, ranges: [], diagnostics });
	        const range = (byteLength) => [{ offset, byteLength, reason: name }];
	        switch (field.kind) {
	            case 'u8':
	                return requireBytes(1) ? { value: ctx.bytes[offset], ranges: range(1), diagnostics } : requiredFieldFailure();
	            case 'u16le':
	                return requireBytes(2) ?
	                    { value: readUInt16LE(ctx.bytes, offset), ranges: range(2), diagnostics }
	                    : requiredFieldFailure();
	            case 'i16le':
	                return requireBytes(2) ?
	                    { value: readInt16LE(ctx.bytes, offset), ranges: range(2), diagnostics }
	                    : requiredFieldFailure();
	            case 'u32le':
	                return requireBytes(4) ?
	                    { value: readUInt32LE(ctx.bytes, offset), ranges: range(4), diagnostics }
	                    : requiredFieldFailure();
	            case 'i32le':
	                return requireBytes(4) ?
	                    { value: readInt32LE(ctx.bytes, offset), ranges: range(4), diagnostics }
	                    : requiredFieldFailure();
	            case 'bytes': {
	                const byteLength = resolveLength(field.length, ctx);
	                if (byteLength === undefined || !requireBytes(byteLength)) {
	                    return requiredFieldFailure();
	                }
	                return { value: ctx.bytes.subarray(offset, offset + byteLength), ranges: range(byteLength), diagnostics };
	            }
	            case 'ascii': {
	                const byteLength = resolveLength(field.length, ctx);
	                if (byteLength === undefined || !requireBytes(byteLength)) {
	                    return requiredFieldFailure();
	                }
	                return { value: bytesToAscii(ctx.bytes, offset, offset + byteLength), ranges: range(byteLength), diagnostics };
	            }
	            case 'fixed-bytes':
	                if (!requireBytes(field.bytes.length) || !hasBytes(ctx.bytes, offset, field.bytes)) {
	                    if (field.optional) {
	                        return undefined;
	                    }
	                    diagnostics.push(fieldDiagnostic(ctx, specName, 'fixed-bytes-mismatch', `Field ${name} does not match fixed bytes`, offset));
	                    return { value: false, ranges: range(field.bytes.length), diagnostics };
	                }
	                return { value: true, ranges: range(field.bytes.length), diagnostics };
	            case 'counted-ascii': {
	                const counted = readCountedAscii(ctx.bytes, offset, field.maxLength ?? 512, field.requireNullTerminator ?? true);
	                if (!counted) {
	                    if (field.optional) {
	                        return undefined;
	                    }
	                    diagnostics.push(fieldDiagnostic(ctx, specName, 'counted-ascii-invalid', `Field ${name} is not valid counted ASCII`, offset));
	                    return { value: undefined, ranges: [], diagnostics };
	                }
	                return { value: counted.value, ranges: range(counted.byteLength), diagnostics };
	            }
	            case 'ascii-runs': {
	                const scanByteLength = field.byteLength === undefined ? undefined : resolveLength(field.byteLength, ctx);
	                const scanEnd = scanByteLength === undefined ? ctx.bytes.length : Math.min(ctx.bytes.length, offset + scanByteLength);
	                const runs = readAsciiRuns(ctx.bytes.subarray(offset, scanEnd), field.minLength ?? 4, field.maxCount ?? 200).map((run) => ({
	                    ...run,
	                    offset: run.offset + offset,
	                }));
	                return {
	                    value: runs,
	                    ranges: runs.map((run) => ({ offset: run.offset, byteLength: run.length, reason: name })),
	                    diagnostics,
	                };
	            }
	            case 'repeated': {
	                const count = typeof field.count === 'function' ? field.count(ctx) : field.count;
	                const itemByteLength = resolveLength(field.byteLength, ctx);
	                if (count === undefined || itemByteLength === undefined || count < 0 || !requireBytes(count * itemByteLength)) {
	                    return requiredFieldFailure();
	                }
	                const values = [];
	                const ranges = [];
	                for (let index = 0; index < count; index++) {
	                    const itemCtx = {
	                        ...ctx,
	                        offset: offset + index * itemByteLength,
	                        fields: {},
	                    };
	                    const item = {};
	                    for (const [itemFieldName, itemField] of Object.entries(field.fields)) {
	                        const itemFieldResult = readField(itemFieldName, itemField, itemCtx, specName);
	                        if (!itemFieldResult && !itemField.optional) {
	                            return undefined;
	                        }
	                        if (itemFieldResult) {
	                            item[itemFieldName] = itemFieldResult.value;
	                            itemCtx.fields[itemFieldName] = itemFieldResult.value;
	                            ranges.push(...itemFieldResult.ranges);
	                            diagnostics.push(...itemFieldResult.diagnostics);
	                        }
	                    }
	                    values.push(item);
	                }
	                return { value: values, ranges, diagnostics };
	            }
	            case 'custom': {
	                const value = field.read({
	                    ...ctx,
	                    offset,
	                });
	                const byteLength = field.byteLength ? resolveLength(field.byteLength, ctx) : undefined;
	                return {
	                    value,
	                    ranges: byteLength !== undefined ? range(byteLength) : [],
	                    diagnostics,
	                };
	            }
	            default:
	                return undefined;
	        }
	    }
	    function mergeRanges(ranges) {
	        const sorted = ranges
	            .filter((range) => range.byteLength > 0)
	            .sort((left, right) => left.offset - right.offset || left.byteLength - right.byteLength);
	        const merged = [];
	        for (const range of sorted) {
	            const previous = merged[merged.length - 1];
	            if (previous && range.offset <= previous.offset + previous.byteLength) {
	                const previousEnd = previous.offset + previous.byteLength;
	                const rangeEnd = range.offset + range.byteLength;
	                previous.byteLength = Math.max(previousEnd, rangeEnd) - previous.offset;
	                previous.reason = previous.reason === range.reason ? previous.reason : 'known-fields';
	                continue;
	            }
	            merged.push({ ...range });
	        }
	        return merged;
	    }
	    function unknownRangesFromKnownRanges(knownRanges, start, byteLength) {
	        const unknownRanges = [];
	        let cursor = start;
	        for (const range of mergeRanges(knownRanges)) {
	            if (range.offset > cursor) {
	                unknownRanges.push({
	                    offset: cursor,
	                    byteLength: range.offset - cursor,
	                    reason: 'unknown',
	                });
	            }
	            cursor = Math.max(cursor, range.offset + range.byteLength);
	        }
	        const end = start + byteLength;
	        if (cursor < end) {
	            unknownRanges.push({
	                offset: cursor,
	                byteLength: end - cursor,
	                reason: 'unknown',
	            });
	        }
	        return unknownRanges;
	    }
	    function decodeRecordSpec(spec, ctx) {
	        if (!spec.match.every((predicate) => predicate(ctx))) {
	            return undefined;
	        }
	        const fieldContext = {
	            ...ctx,
	            fields: {},
	        };
	        const diagnostics = [];
	        const knownRanges = [...(spec.knownRanges ?? [])];
	        for (const [name, field] of Object.entries(spec.fields)) {
	            const result = readField(name, field, fieldContext, spec.name);
	            if (!result) {
	                continue;
	            }
	            fieldContext.fields[name] = result.value;
	            knownRanges.push(...result.ranges);
	            diagnostics.push(...result.diagnostics);
	        }
	        if (spec.validate && !spec.validate.every((predicate) => predicate(fieldContext))) {
	            return undefined;
	        }
	        const value = spec.decode ? spec.decode(fieldContext) : spec.derive?.(fieldContext.fields ?? {}, fieldContext);
	        if (value === undefined) {
	            return undefined;
	        }
	        const valueByteLength = (typeof value === 'object' &&
	            value !== null &&
	            'byteLength' in value &&
	            typeof value.byteLength === 'number') ?
	            value.byteLength
	            : undefined;
	        const spanOffset = ctx.payloadOffset ?? ctx.offset;
	        const spanByteLength = ctx.byteLength ?? ctx.payloadByteLength ?? valueByteLength ?? ctx.bytes.byteLength - ctx.offset;
	        const rangeOffsetDelta = spanOffset - ctx.offset;
	        const unknownRanges = unknownRangesFromKnownRanges(knownRanges, ctx.offset, spanByteLength).map((range) => ({
	            ...range,
	            offset: range.offset + rangeOffsetDelta,
	        }));
	        return {
	            kind: spec.kind,
	            family: spec.family,
	            value,
	            span: {
	                streamPath: ctx.streamPath,
	                offset: spanOffset,
	                byteLength: spanByteLength,
	                ...(ctx.payloadOffset !== undefined ? { payloadOffset: ctx.payloadOffset } : {}),
	                ...(ctx.payloadByteLength !== undefined ? { payloadByteLength: ctx.payloadByteLength } : {}),
	            },
	            specName: spec.name,
	            confidence: spec.confidence ?? 'exact',
	            diagnostics,
	            ...(unknownRanges.length > 0 ? { unknownRanges } : {}),
	        };
	    }
	    DsnCfbBinarySpec.decodeRecordSpec = decodeRecordSpec;
	    function matchStreamSpecs(specs, entry) {
	        return specs.filter((spec) => (spec.path instanceof RegExp ? spec.path.test(entry.path) : spec.path(entry)));
	    }
	    DsnCfbBinarySpec.matchStreamSpecs = matchStreamSpecs;
	})(DsnCfbBinarySpec$1 || (DsnCfbBinarySpec.DsnCfbBinarySpec = DsnCfbBinarySpec$1 = {}));
	
	return DsnCfbBinarySpec;
}

var OrcadDsnCfbDeclarativeSpecs = {};

var OrcadDsnCfbStreamUtils = {};

var OrcadDsnCfbTypes = {};

var hasRequiredOrcadDsnCfbTypes;

function requireOrcadDsnCfbTypes () {
	if (hasRequiredOrcadDsnCfbTypes) return OrcadDsnCfbTypes;
	hasRequiredOrcadDsnCfbTypes = 1;
	Object.defineProperty(OrcadDsnCfbTypes, "__esModule", { value: true });
	OrcadDsnCfbTypes.OrcadDsnCfbTypes = void 0;
	var OrcadDsnCfbTypes$1;
	(function (OrcadDsnCfbTypes) {
	    (function (PinNameSource) {
	        PinNameSource["Record"] = "record";
	        PinNameSource["SymbolPin"] = "symbol_pin";
	    })(OrcadDsnCfbTypes.PinNameSource || (OrcadDsnCfbTypes.PinNameSource = {}));
	    (function (PlacementReferenceState) {
	        PlacementReferenceState["Resolved"] = "resolved";
	        PlacementReferenceState["Withheld"] = "withheld";
	    })(OrcadDsnCfbTypes.PlacementReferenceState || (OrcadDsnCfbTypes.PlacementReferenceState = {}));
	    (function (PlacementValueState) {
	        PlacementValueState["Withheld"] = "withheld";
	    })(OrcadDsnCfbTypes.PlacementValueState || (OrcadDsnCfbTypes.PlacementValueState = {}));
	    (function (OrCadDsnCfbSymbolPinType) {
	        OrCadDsnCfbSymbolPinType[OrCadDsnCfbSymbolPinType["Power"] = 7] = "Power";
	    })(OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinType || (OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinType = {}));
	    let OrCadDsnCfbSymbolPinDisplayFlag;
	    (function (OrCadDsnCfbSymbolPinDisplayFlag) {
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsLong"] = 1] = "IsLong";
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsClock"] = 2] = "IsClock";
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsDot"] = 4] = "IsDot";
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsLeftPointing"] = 8] = "IsLeftPointing";
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsRightPointing"] = 16] = "IsRightPointing";
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsVisible"] = 32] = "IsVisible";
	        OrCadDsnCfbSymbolPinDisplayFlag[OrCadDsnCfbSymbolPinDisplayFlag["IsHidden"] = 128] = "IsHidden";
	    })(OrCadDsnCfbSymbolPinDisplayFlag = OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinDisplayFlag || (OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinDisplayFlag = {}));
	    OrcadDsnCfbTypes.ORCAD_DSN_CFB_KNOWN_SYMBOL_PIN_DISPLAY_FLAGS = OrCadDsnCfbSymbolPinDisplayFlag.IsLong |
	        OrCadDsnCfbSymbolPinDisplayFlag.IsClock |
	        OrCadDsnCfbSymbolPinDisplayFlag.IsDot |
	        OrCadDsnCfbSymbolPinDisplayFlag.IsLeftPointing |
	        OrCadDsnCfbSymbolPinDisplayFlag.IsRightPointing |
	        OrCadDsnCfbSymbolPinDisplayFlag.IsVisible |
	        OrCadDsnCfbSymbolPinDisplayFlag.IsHidden;
	    (function (OrCadDsnCfbMetadataFieldState) {
	        OrCadDsnCfbMetadataFieldState["stated"] = "stated";
	        OrCadDsnCfbMetadataFieldState["empty"] = "empty";
	        OrCadDsnCfbMetadataFieldState["malformed"] = "malformed";
	    })(OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState || (OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState = {}));
	    (function (HierarchyOwnershipState) {
	        HierarchyOwnershipState["Resolved"] = "resolved";
	        HierarchyOwnershipState["Withheld"] = "withheld";
	    })(OrcadDsnCfbTypes.HierarchyOwnershipState || (OrcadDsnCfbTypes.HierarchyOwnershipState = {}));
	    (function (HierarchyBranchDecodeState) {
	        HierarchyBranchDecodeState["Resolved"] = "resolved";
	        HierarchyBranchDecodeState["Withheld"] = "withheld";
	        HierarchyBranchDecodeState["Unframed"] = "unframed";
	        HierarchyBranchDecodeState["Incomplete"] = "incomplete";
	    })(OrcadDsnCfbTypes.HierarchyBranchDecodeState || (OrcadDsnCfbTypes.HierarchyBranchDecodeState = {}));
	})(OrcadDsnCfbTypes$1 || (OrcadDsnCfbTypes.OrcadDsnCfbTypes = OrcadDsnCfbTypes$1 = {}));
	
	return OrcadDsnCfbTypes;
}

var hasRequiredOrcadDsnCfbStreamUtils;

function requireOrcadDsnCfbStreamUtils () {
	if (hasRequiredOrcadDsnCfbStreamUtils) return OrcadDsnCfbStreamUtils;
	hasRequiredOrcadDsnCfbStreamUtils = 1;
	Object.defineProperty(OrcadDsnCfbStreamUtils, "__esModule", { value: true });
	OrcadDsnCfbStreamUtils.OrcadDsnCfbStreamUtils = void 0;
	const DsnCfbBinarySpec_1 = requireDsnCfbBinarySpec();
	const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
	var OrcadDsnCfbStreamUtils$1;
	(function (OrcadDsnCfbStreamUtils) {
	    OrcadDsnCfbStreamUtils.ROOT_PREFIX = 'Root Entry/';
	    OrcadDsnCfbStreamUtils.RECORD_MAGIC = [0xff, 0xe4, 0x5c, 0x39];
	    OrcadDsnCfbStreamUtils.RECORD_TERMINATOR = [0xff, 0xe4, 0x5c, 0x39, 0x00, 0x00, 0x00, 0x00];
	    OrcadDsnCfbStreamUtils.LIBRARY_INSTANCE_MARKER_PATTERN = /^INS\d+(?:~\d*)?$/;
	    function isLibraryInstanceMarker(value) {
	        return OrcadDsnCfbStreamUtils.LIBRARY_INSTANCE_MARKER_PATTERN.test(value);
	    }
	    OrcadDsnCfbStreamUtils.isLibraryInstanceMarker = isLibraryInstanceMarker;
	    OrcadDsnCfbStreamUtils.readUInt16LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt16LE;
	    OrcadDsnCfbStreamUtils.readUInt32LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt32LE;
	    OrcadDsnCfbStreamUtils.bytesToAscii = DsnCfbBinarySpec_1.DsnCfbBinarySpec.bytesToAscii;
	    OrcadDsnCfbStreamUtils.isPrintableAsciiByte = DsnCfbBinarySpec_1.DsnCfbBinarySpec.isPrintableAsciiByte;
	    OrcadDsnCfbStreamUtils.hasBytes = DsnCfbBinarySpec_1.DsnCfbBinarySpec.hasBytes;
	    function readLengthPrefixedAsciiStringAt(bytes, offset, options = {}) {
	        const maxLength = options.maxLength ?? 512;
	        const requireNullTerminator = options.requireNullTerminator ?? true;
	        if (offset + 2 > bytes.length) {
	            return undefined;
	        }
	        const counted = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readCountedAscii(bytes, offset, maxLength, requireNullTerminator);
	        if (!counted) {
	            return undefined;
	        }
	        const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	        const hasNullTerminator = counted.byteLength === 2 + length + 1;
	        return {
	            offset,
	            length,
	            value: counted.value,
	            hasNullTerminator,
	        };
	    }
	    OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt = readLengthPrefixedAsciiStringAt;
	    function lengthPrefixedStringEnd(string) {
	        return string.offset + 2 + string.length + (string.hasNullTerminator ? 1 : 0);
	    }
	    OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd = lengthPrefixedStringEnd;
	    function detectRecordHeader(bytes, offset = 0) {
	        if (offset + 9 > bytes.length) {
	            return undefined;
	        }
	        const payloadLength = OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
	        const totalLength = payloadLength + 9;
	        if (payloadLength > bytes.length - offset - 9) {
	            return undefined;
	        }
	        return {
	            offset,
	            type: bytes[offset],
	            payloadLength,
	            reserved: OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5),
	            totalLength,
	        };
	    }
	    OrcadDsnCfbStreamUtils.detectRecordHeader = detectRecordHeader;
	    function detectLeadingRecordHeaders(bytes, maxRecords = 32) {
	        const records = [];
	        let offset = 0;
	        while (records.length < maxRecords) {
	            const header = detectRecordHeader(bytes, offset);
	            if (!header) {
	                break;
	            }
	            records.push(header);
	            if (header.totalLength <= 0) {
	                break;
	            }
	            offset += header.totalLength;
	        }
	        return records;
	    }
	    OrcadDsnCfbStreamUtils.detectLeadingRecordHeaders = detectLeadingRecordHeaders;
	    function readCountedAsciiField(bytes, offset) {
	        if (offset + 2 > bytes.length) {
	            return undefined;
	        }
	        const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	        const valueStart = offset + 2;
	        const valueEnd = valueStart + length;
	        if (length > MAX_METADATA_FIELD_LENGTH || valueEnd >= bytes.length) {
	            return undefined;
	        }
	        for (let index = valueStart; index < valueEnd; index++) {
	            const byte = bytes[index];
	            if (byte < 0x20 || byte > 0x7e) {
	                return undefined;
	            }
	        }
	        if (bytes[valueEnd] !== 0) {
	            return undefined;
	        }
	        return { value: latin1String(bytes, valueStart, valueEnd), byteLength: 2 + length + 1 };
	    }
	    OrcadDsnCfbStreamUtils.readCountedAsciiField = readCountedAsciiField;
	    const MAX_METADATA_FIELD_LENGTH = 512;
	    const SHEET_SYMBOL_METADATA_FIELDS = 3;
	    const CELL_VIEW_NAME_PATTERN = /\.(?:Normal|Convert|IEEE)$/i;
	    function readCellViewHeader(bytes, offset) {
	        const terminatorStart = offset - OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length;
	        if (terminatorStart < 0) {
	            return undefined;
	        }
	        for (let index = 0; index < OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length; index++) {
	            if (bytes[terminatorStart + index] !== OrcadDsnCfbStreamUtils.RECORD_TERMINATOR[index]) {
	                return undefined;
	            }
	        }
	        const name = readCountedAsciiField(bytes, offset);
	        if (!name || !CELL_VIEW_NAME_PATTERN.test(name.value)) {
	            return undefined;
	        }
	        const library = readCountedAsciiField(bytes, offset + name.byteLength);
	        return { value: name.value, byteLength: name.byteLength + (library?.byteLength ?? 0) };
	    }
	    function extractSheetSymbolMetadataRecords(bytes) {
	        const records = [];
	        let cellViewName;
	        for (let offset = 0; offset + 2 <= bytes.length; offset++) {
	            const header = readCellViewHeader(bytes, offset);
	            if (header) {
	                cellViewName = header.value;
	                offset += header.byteLength - 1;
	                continue;
	            }
	            const path = readCountedAsciiField(bytes, offset);
	            if (!path || path.value.length === 0) {
	                continue;
	            }
	            if (path.value.length < 5 || !/\.dsn$/i.test(path.value)) {
	                continue;
	            }
	            const fields = [];
	            let cursor = offset + path.byteLength;
	            let malformed = false;
	            for (let index = 0; index < SHEET_SYMBOL_METADATA_FIELDS; index++) {
	                const field = malformed ? undefined : readCountedAsciiField(bytes, cursor);
	                if (!field) {
	                    malformed = true;
	                    fields.push({ state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState.malformed });
	                    continue;
	                }
	                cursor += field.byteLength;
	                fields.push(field.value.length === 0 ?
	                    { state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState.empty }
	                    : { state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState.stated, value: field.value });
	            }
	            records.push({
	                offset,
	                contentsLibName: path.value,
	                ...(cellViewName ? { cellViewName } : {}),
	                view: fields[0],
	                symbolClass: fields[1],
	                partValue: fields[2],
	            });
	            offset += path.byteLength - 1;
	        }
	        return records;
	    }
	    OrcadDsnCfbStreamUtils.extractSheetSymbolMetadataRecords = extractSheetSymbolMetadataRecords;
	    function extractLengthPrefixedAsciiStrings(bytes, options = {}) {
	        const minLength = options.minLength ?? 1;
	        const maxLength = options.maxLength ?? 512;
	        const maxCount = options.maxCount ?? 200;
	        const requireNullTerminator = options.requireNullTerminator ?? true;
	        const strings = [];
	        for (let offset = 0; offset + 2 <= bytes.length && strings.length < maxCount; offset++) {
	            const counted = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readCountedAscii(bytes, offset, maxLength, requireNullTerminator);
	            if (!counted) {
	                continue;
	            }
	            const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	            if (length < minLength) {
	                continue;
	            }
	            const hasNullTerminator = counted.byteLength === 2 + length + 1;
	            strings.push({
	                offset,
	                length,
	                value: counted.value,
	                hasNullTerminator,
	                ...(offset >= 20 ?
	                    {
	                        precedingInt32Le20: DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt32LE(bytes, offset - 20),
	                        precedingInt32Le16: DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt32LE(bytes, offset - 16),
	                    }
	                    : {}),
	                ...(offset + counted.byteLength + 4 <= bytes.length ?
	                    { followingUInt32Le: OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + counted.byteLength) }
	                    : {}),
	            });
	            offset += counted.byteLength - 1;
	        }
	        return strings;
	    }
	    OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings = extractLengthPrefixedAsciiStrings;
	    function latin1String(bytes, start, end) {
	        const chunkSize = 0x8000;
	        let value = '';
	        for (let offset = start; offset < end; offset += chunkSize) {
	            value += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + chunkSize, end)));
	        }
	        return value;
	    }
	    function readContiguousCountedStrings(bytes, start, count) {
	        const values = [];
	        let offset = start;
	        for (let index = 0; index < count; index++) {
	            if (offset + 2 > bytes.length) {
	                return undefined;
	            }
	            const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	            const terminatorOffset = offset + 2 + length;
	            if (terminatorOffset >= bytes.length || bytes[terminatorOffset] !== 0x00) {
	                return undefined;
	            }
	            values.push(latin1String(bytes, offset + 2, terminatorOffset));
	            offset = terminatorOffset + 1;
	        }
	        return { values, end: offset };
	    }
	    function computeFrameChainReachesWindow(bytes, windowStart) {
	        const reachesWindow = new Uint8Array(bytes.length + 1);
	        for (let offset = bytes.length; offset >= 0; offset--) {
	            if (offset >= windowStart) {
	                reachesWindow[offset] = 1;
	                continue;
	            }
	            if (offset + 2 > bytes.length) {
	                continue;
	            }
	            const length = OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	            const terminatorOffset = offset + 2 + length;
	            if (terminatorOffset >= bytes.length || bytes[terminatorOffset] !== 0x00) {
	                continue;
	            }
	            reachesWindow[offset] = reachesWindow[terminatorOffset + 1];
	        }
	        return reachesWindow;
	    }
	    function extractLibraryStringCatalog(bytes) {
	        const maxTrailingBytes = 64;
	        const windowStart = Math.max(0, bytes.length - maxTrailingBytes);
	        const reachesWindow = computeFrameChainReachesWindow(bytes, windowStart);
	        for (let offset = 0; offset + 4 <= bytes.length; offset++) {
	            const count = OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	            if (count < 8 || count > 2_000_000) {
	                continue;
	            }
	            if (!reachesWindow[offset + 4]) {
	                continue;
	            }
	            const catalog = readContiguousCountedStrings(bytes, offset + 4, count);
	            if (catalog && bytes.length - catalog.end <= maxTrailingBytes) {
	                return catalog.values;
	            }
	        }
	        return undefined;
	    }
	    OrcadDsnCfbStreamUtils.extractLibraryStringCatalog = extractLibraryStringCatalog;
	    function extractAsciiRuns(bytes, options = {}) {
	        const minLength = options.minLength ?? 4;
	        const maxCount = options.maxCount ?? 200;
	        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.readAsciiRuns(bytes, minLength, maxCount);
	    }
	    OrcadDsnCfbStreamUtils.extractAsciiRuns = extractAsciiRuns;
	    function forEachTerminatedPayload(bytes, callback) {
	        let payloadOffset = 0;
	        for (let offset = 0; offset <= bytes.length - OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length; offset++) {
	            if (!OrcadDsnCfbStreamUtils.hasBytes(bytes, offset, OrcadDsnCfbStreamUtils.RECORD_TERMINATOR)) {
	                continue;
	            }
	            callback({
	                offset: payloadOffset,
	                payloadByteLength: offset - payloadOffset,
	                byteLength: offset + OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length - payloadOffset,
	                payload: bytes.subarray(payloadOffset, offset),
	            });
	            payloadOffset = offset + OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length;
	            offset = payloadOffset - 1;
	        }
	        if (payloadOffset < bytes.length) {
	            callback({
	                offset: payloadOffset,
	                payloadByteLength: bytes.length - payloadOffset,
	                byteLength: bytes.length - payloadOffset,
	                payload: bytes.subarray(payloadOffset),
	            });
	        }
	    }
	    OrcadDsnCfbStreamUtils.forEachTerminatedPayload = forEachTerminatedPayload;
	    function forEachPagePayload(bytes, callback) {
	        let payloadOffset = 0;
	        for (let offset = 0; offset <= bytes.length - OrcadDsnCfbStreamUtils.RECORD_MAGIC.length; offset++) {
	            if (!OrcadDsnCfbStreamUtils.hasBytes(bytes, offset, OrcadDsnCfbStreamUtils.RECORD_MAGIC)) {
	                continue;
	            }
	            callback({
	                offset: payloadOffset,
	                payloadByteLength: offset - payloadOffset,
	                byteLength: offset + OrcadDsnCfbStreamUtils.RECORD_MAGIC.length - payloadOffset,
	                payload: bytes.subarray(payloadOffset, offset),
	            });
	            const hasZeroTerminatorTail = offset + OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length <= bytes.length && OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4) === 0;
	            payloadOffset = offset + (hasZeroTerminatorTail ? OrcadDsnCfbStreamUtils.RECORD_TERMINATOR.length : OrcadDsnCfbStreamUtils.RECORD_MAGIC.length);
	            offset = payloadOffset - 1;
	        }
	        if (payloadOffset < bytes.length) {
	            callback({
	                offset: payloadOffset,
	                payloadByteLength: bytes.length - payloadOffset,
	                byteLength: bytes.length - payloadOffset,
	                payload: bytes.subarray(payloadOffset),
	            });
	        }
	    }
	    OrcadDsnCfbStreamUtils.forEachPagePayload = forEachPagePayload;
	    function isDecodedObjectId(value) {
	        return value !== undefined && Number.isInteger(value) && value > 0 && value < 0xffff_ffff;
	    }
	    OrcadDsnCfbStreamUtils.isDecodedObjectId = isDecodedObjectId;
	    function stripRoot(path) {
	        if (path === 'Root Entry/' || path === '/') {
	            return '';
	        }
	        return path.startsWith(OrcadDsnCfbStreamUtils.ROOT_PREFIX) ? path.slice(OrcadDsnCfbStreamUtils.ROOT_PREFIX.length) : path;
	    }
	    OrcadDsnCfbStreamUtils.stripRoot = stripRoot;
	})(OrcadDsnCfbStreamUtils$1 || (OrcadDsnCfbStreamUtils.OrcadDsnCfbStreamUtils = OrcadDsnCfbStreamUtils$1 = {}));
	
	return OrcadDsnCfbStreamUtils;
}

var hasRequiredOrcadDsnCfbDeclarativeSpecs;

function requireOrcadDsnCfbDeclarativeSpecs () {
	if (hasRequiredOrcadDsnCfbDeclarativeSpecs) return OrcadDsnCfbDeclarativeSpecs;
	hasRequiredOrcadDsnCfbDeclarativeSpecs = 1;
	Object.defineProperty(OrcadDsnCfbDeclarativeSpecs, "__esModule", { value: true });
	OrcadDsnCfbDeclarativeSpecs.OrcadDsnCfbDeclarativeSpecs = void 0;
	const DsnCfbBinarySpec_1 = requireDsnCfbBinarySpec();
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	var OrcadDsnCfbDeclarativeSpecs$1;
	(function (OrcadDsnCfbDeclarativeSpecs) {
	    const RECORD_TERMINATOR = [0xff, 0xe4, 0x5c, 0x39, 0x00, 0x00, 0x00, 0x00];
	    const hasBytes = DsnCfbBinarySpec_1.DsnCfbBinarySpec.hasBytes;
	    const readUInt16LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt16LE;
	    const readInt16LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt16LE;
	    const readUInt32LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt32LE;
	    const readInt32LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt32LE;
	    const bytesToAscii = DsnCfbBinarySpec_1.DsnCfbBinarySpec.bytesToAscii;
	    const bytesToHex = DsnCfbBinarySpec_1.DsnCfbBinarySpec.bytesToHex;
	    const isPrintableAsciiByte = DsnCfbBinarySpec_1.DsnCfbBinarySpec.isPrintableAsciiByte;
	    function hasRecordTerminator(bytes, offset) {
	        return hasBytes(bytes, offset, RECORD_TERMINATOR);
	    }
	    function isPlausibleCoordinate(value) {
	        return Number.isInteger(value) && Math.abs(value) <= 1_000_000;
	    }
	    function arePlausibleCoordinates(values) {
	        return values.every(isPlausibleCoordinate);
	    }
	    function numberField(fields, name) {
	        const value = fields[name];
	        return typeof value === 'number' ? value : undefined;
	    }
	    function libraryCatalogValue(ctx, index) {
	        const catalog = Array.isArray(ctx.extra?.libraryStringCatalog) ? ctx.extra.libraryStringCatalog : undefined;
	        if (catalog === undefined) {
	            return undefined;
	        }
	        const value = catalog[index];
	        return typeof value === 'string' && value !== '' ? value : undefined;
	    }
	    function stringField(fields, name) {
	        const value = fields[name];
	        return typeof value === 'string' ? value : undefined;
	    }
	    function stringArrayField(fields, name) {
	        const value = fields[name];
	        return Array.isArray(value) ? value : [];
	    }
	    function terminatedPayloadFromContext(ctx) {
	        return {
	            offset: ctx.payloadOffset ?? ctx.offset,
	            payloadByteLength: ctx.payloadByteLength ?? ctx.bytes.byteLength - ctx.offset,
	            byteLength: ctx.byteLength ?? ctx.payloadByteLength ?? ctx.bytes.byteLength - ctx.offset,
	            payload: ctx.bytes.subarray(ctx.offset),
	        };
	    }
	    function countedStringsFromContext(ctx, options = {}) {
	        return OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(ctx.bytes.subarray(ctx.offset), {
	            maxCount: options.maxCount ?? 64,
	            maxLength: options.maxLength ?? 512,
	            requireNullTerminator: options.requireNullTerminator ?? true,
	        }).map((string) => ({ ...string, offset: string.offset + ctx.offset }));
	    }
	    function leadingCountedStringEndFromContext(ctx) {
	        const string = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(ctx.bytes, ctx.offset, { maxLength: 256 });
	        return string ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string) : undefined;
	    }
	    function isStreamViewName(value, streamName) {
	        return value === streamName || value.endsWith('.Normal') || value.endsWith('.Convert') || value.endsWith('.IEEE');
	    }
	    function isCapsymPowerSymbolName(name) {
	        return name === '0' || /^(?:GND|VCC)/i.test(name);
	    }
	    OrcadDsnCfbDeclarativeSpecs.isCapsymPowerSymbolName = isCapsymPowerSymbolName;
	    function isCacheDefinedConnectionSymbol(symbolName, knownSpecialCacheSymbols) {
	        const evidence = knownSpecialCacheSymbols?.get(symbolName);
	        if (evidence === undefined) {
	            return false;
	        }
	        return evidence.pinCount >= 1 || evidence.decodeIncomplete;
	    }
	    function isPinlessCacheSymbol(symbolName, knownSpecialCacheSymbols) {
	        const evidence = knownSpecialCacheSymbols?.get(symbolName);
	        return evidence !== undefined && evidence.pinCount === 0 && !evidence.decodeIncomplete;
	    }
	    function isIndeterminateCacheSymbol(symbolName, knownSpecialCacheSymbols) {
	        const evidence = knownSpecialCacheSymbols?.get(symbolName);
	        return evidence !== undefined && evidence.pinCount === 0 && evidence.decodeIncomplete;
	    }
	    function isPortConventionSymbolName(symbolName) {
	        return /^PORT/i.test(symbolName);
	    }
	    function isDirectedCacheConnectionSymbol(symbolName, knownSpecialCacheSymbols) {
	        const evidence = knownSpecialCacheSymbols?.get(symbolName);
	        return evidence?.hasDirectedConnectionPin === true && evidence.directionConflicted !== true;
	    }
	    function hasConflictedCacheConnectionDirection(symbolName, knownSpecialCacheSymbols) {
	        return knownSpecialCacheSymbols?.get(symbolName)?.directionConflicted === true;
	    }
	    function isPowerCacheConnectionSymbol(symbolName, knownSpecialCacheSymbols) {
	        const evidence = knownSpecialCacheSymbols?.get(symbolName);
	        return evidence?.hasPowerConnectionPin === true && !evidence.hasDirectedConnectionPin && evidence.pinCount === 1;
	    }
	    function classifyPlacedSymbolRole(symbolName, extras, streamPath, offset) {
	        if (symbolName.startsWith('TitleBlock') || symbolName === 'Cadence') {
	            return 'titleblock';
	        }
	        if (symbolName.startsWith('OFFPAGE') || symbolName.startsWith('PAGE-')) {
	            return 'off-page-connector';
	        }
	        if (symbolName.endsWith('.Normal') || symbolName.endsWith('.Convert') || symbolName.endsWith('.IEEE')) {
	            return 'part-inst';
	        }
	        if (extras.knownSpecialCacheSymbols?.get(symbolName)?.conflictingDefinitions === true) {
	            extras.diagnostics?.push({
	                severity: 'warning',
	                code: 'cache-symbol-definitions-conflict',
	                message: `two cache groups named "${symbolName}" draw its connection pin differently, so the name stands for more than one symbol and this placement's kind is unknown`,
	                ...(streamPath === undefined ? {} : { streamPath }),
	                ...(offset === undefined ? {} : { offset }),
	            });
	            return 'unknown';
	        }
	        if (isPowerCacheConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
	            return 'global';
	        }
	        if (isCapsymPowerSymbolName(symbolName)) {
	            extras.diagnostics?.push({
	                severity: 'info',
	                code: 'global-from-capsym-name',
	                message: `no cache group defines a connection pin for "${symbolName}", so it is read as a global on Capture's own CAPSYM naming alone`,
	                ...(streamPath === undefined ? {} : { streamPath }),
	                ...(offset === undefined ? {} : { offset }),
	            });
	            return 'global';
	        }
	        if (!isCacheDefinedConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
	            if (isPinlessCacheSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
	                extras.diagnostics?.push({
	                    severity: 'info',
	                    code: 'cache-symbol-without-pins',
	                    message: `cache group named "${symbolName}" decoded no pins, so it is read as package bookkeeping rather than a placed connector`,
	                    ...(streamPath === undefined ? {} : { streamPath }),
	                    ...(offset === undefined ? {} : { offset }),
	                });
	            }
	            return 'unknown';
	        }
	        if (isIndeterminateCacheSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
	            extras.diagnostics?.push({
	                severity: 'warning',
	                code: 'cache-symbol-decode-incomplete',
	                message: `cache group named "${symbolName}" did not decode cleanly, so its pin structure is indeterminate; the placement is kept on the cache classification alone`,
	                ...(streamPath === undefined ? {} : { streamPath }),
	                ...(offset === undefined ? {} : { offset }),
	            });
	        }
	        if (isDirectedCacheConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
	            return 'port';
	        }
	        if (hasConflictedCacheConnectionDirection(symbolName, extras.knownSpecialCacheSymbols)) {
	            extras.diagnostics?.push({
	                severity: 'warning',
	                code: 'connector-role-ambiguous',
	                message: `cache definitions named "${symbolName}" disagree about their connection pin's direction, so the name settles no connector kind; the placement is left unclassified`,
	                ...(streamPath === undefined ? {} : { streamPath }),
	                ...(offset === undefined ? {} : { offset }),
	            });
	            return 'unknown';
	        }
	        if (isPortConventionSymbolName(symbolName)) {
	            return 'port';
	        }
	        extras.diagnostics?.push({
	            severity: 'warning',
	            code: 'ambiguous-connector-role',
	            message: `cache-defined connector symbolName="${symbolName}" follows no port naming convention and its pin is neither a power pin nor a directed one, so it is read as an off-page connector`,
	            ...(streamPath === undefined ? {} : { streamPath }),
	            ...(offset === undefined ? {} : { offset }),
	        });
	        return 'off-page-connector';
	    }
	    function wireJunctionsFromMarker(childMarker, startX, startY, endX, endY) {
	        const junctions = [];
	        if ((childMarker & 0x01) !== 0) {
	            junctions.push({ endpoint: 'start', locX: startX, locY: startY });
	        }
	        if ((childMarker & 0x02) !== 0) {
	            junctions.push({ endpoint: 'end', locX: endX, locY: endY });
	        }
	        return junctions;
	    }
	    const PAGE_HEADER_MANIFEST_RECORD_TYPE = 0x00036add;
	    const UNSET_OBJECT_ID = 0xffff_ffff;
	    function placedSymbolDecodeExtras(ctx) {
	        const extra = ctx.extra;
	        return {
	            placementDisplayCounts: extra?.placementDisplayCounts,
	            knownSpecialCacheSymbols: extra?.knownSpecialSymbols instanceof Map ? extra.knownSpecialSymbols : undefined,
	            diagnostics: extra?.roleDiagnostics,
	        };
	    }
	    function placedSymbolCandidateAt(record, symbolNameLengthOffset, streamPath, extras = {}) {
	        const bytes = record.payload;
	        if (bytes.length < 32 ||
	            bytes.length > 512 ||
	            symbolNameLengthOffset < 4 ||
	            symbolNameLengthOffset + 2 > bytes.length) {
	            return undefined;
	        }
	        const markerOffset = symbolNameLengthOffset - 4;
	        const marker = readUInt32LE(bytes, markerOffset);
	        const symbolNameLength = readUInt16LE(bytes, symbolNameLengthOffset);
	        const symbolNameStart = symbolNameLengthOffset + 2;
	        const symbolNameEnd = symbolNameStart + symbolNameLength;
	        if (symbolNameLength < 1 ||
	            symbolNameLength > 256 ||
	            symbolNameEnd >= bytes.length ||
	            bytes[symbolNameEnd] !== 0x00) {
	            return undefined;
	        }
	        for (let offset = symbolNameStart; offset < symbolNameEnd; offset++) {
	            if (!isPrintableAsciiByte(bytes[offset])) {
	                return undefined;
	            }
	        }
	        const dataOffset = symbolNameEnd + 1;
	        if (dataOffset + 18 > bytes.length) {
	            return undefined;
	        }
	        const dbId = readUInt32LE(bytes, dataOffset);
	        const locX = readInt16LE(bytes, dataOffset + 12);
	        const locY = readInt16LE(bytes, dataOffset + 14);
	        const color = bytes[dataOffset + 16];
	        const flags = bytes[dataOffset + 17];
	        if (dbId === 0 || dbId === UNSET_OBJECT_ID || color > 255 || !arePlausibleCoordinates([locX, locY])) {
	            return undefined;
	        }
	        const symbolName = bytesToAscii(bytes, symbolNameStart, symbolNameEnd);
	        if (symbolName === '0' && color !== 48) {
	            return undefined;
	        }
	        const role = classifyPlacedSymbolRole(symbolName, extras, streamPath, record.offset);
	        if (role === 'unknown' && !isCacheDefinedConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
	            extras.diagnostics?.push({
	                severity: 'info',
	                code: 'placed-symbol-role-rejected',
	                message: `placed-symbol candidate dropped: unrecognized role for symbolName="${symbolName}" dbId=${dbId}`,
	                streamPath,
	                offset: record.offset,
	            });
	            return undefined;
	        }
	        return {
	            role,
	            displayCountOffset: record.offset + dataOffset + 20,
	            symbolName,
	            graphicName: symbolName,
	            dbId,
	            locX,
	            locY,
	            color,
	            recordType: readUInt32LE(bytes, 0),
	            marker,
	            flags,
	        };
	    }
	    function isEmbeddedPlacedSymbolName(value, extras) {
	        if (/\.(?:Normal|IEEE)$/.test(value)) {
	            return true;
	        }
	        if (/^(?:PORT|OFFPAGE)/i.test(value)) {
	            return true;
	        }
	        return isCacheDefinedConnectionSymbol(value, extras.knownSpecialCacheSymbols);
	    }
	    function placedSymbolCandidatesFromContext(ctx) {
	        const record = terminatedPayloadFromContext(ctx);
	        const extras = placedSymbolDecodeExtras(ctx);
	        const simpleSymbol = placedSymbolCandidateAt(record, 8, ctx.streamPath, extras);
	        if (simpleSymbol) {
	            return [simpleSymbol];
	        }
	        if (readUInt32LE(record.payload, 0) === PAGE_HEADER_MANIFEST_RECORD_TYPE) {
	            return [];
	        }
	        for (const string of OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(record.payload, {
	            maxCount: 64,
	            maxLength: 256,
	        })) {
	            if (string.offset === 8 || !isEmbeddedPlacedSymbolName(string.value, extras)) {
	                continue;
	            }
	            const embeddedSymbol = placedSymbolCandidateAt(record, string.offset, ctx.streamPath, extras);
	            if (embeddedSymbol) {
	                return [embeddedSymbol];
	            }
	        }
	        for (let offset = 4; offset + 2 <= record.payload.length; offset++) {
	            const length = readUInt16LE(record.payload, offset);
	            const end = offset + 2 + length;
	            if (length < 8 || end >= record.payload.length || record.payload[end] !== 0x00) {
	                continue;
	            }
	            const name = bytesToAscii(record.payload, offset + 2, end);
	            if (!name.endsWith('.Normal') && !name.endsWith('.Convert') && !name.endsWith('.IEEE')) {
	                continue;
	            }
	            const nestedSymbol = placedSymbolCandidateAt(record, offset, ctx.streamPath, extras);
	            if (nestedSymbol) {
	                return [nestedSymbol];
	            }
	        }
	        return [];
	    }
	    function placedSymbolCandidateField(fields) {
	        const candidates = fields.candidates;
	        return Array.isArray(candidates) ? candidates[0] : undefined;
	    }
	    function propertyIdPairsAt(payload, count) {
	        const pairsOffset = payload.length - count * 8;
	        const pairs = [];
	        for (let index = 0; index < count; index++) {
	            const nameId = readUInt32LE(payload, pairsOffset + index * 8);
	            const valueId = readUInt32LE(payload, pairsOffset + index * 8 + 4);
	            if (nameId === 0 || nameId > 10_000_000 || valueId > 10_000_000) {
	                return undefined;
	            }
	            pairs.push({ nameId, valueId });
	        }
	        return pairs;
	    }
	    const PropertyIdPairBlockTags = new Set([0x0d, 0x41]);
	    function decodeTailPropertyIdPairs(payload) {
	        const maxPairs = Math.min(512, Math.floor((payload.length - 3) / 8));
	        let unrecognizedTag;
	        for (let count = 1; count <= maxPairs; count++) {
	            const headerOffset = payload.length - count * 8 - 3;
	            if (readUInt16LE(payload, headerOffset + 1) !== count) {
	                continue;
	            }
	            const pairs = propertyIdPairsAt(payload, count);
	            if (!pairs) {
	                continue;
	            }
	            if (PropertyIdPairBlockTags.has(payload[headerOffset])) {
	                return { pairs: pairs.reverse() };
	            }
	            unrecognizedTag = unrecognizedTag ?? payload[headerOffset];
	        }
	        return unrecognizedTag === undefined ? {} : { unrecognizedTag };
	    }
	    OrcadDsnCfbDeclarativeSpecs.decodeTailPropertyIdPairs = decodeTailPropertyIdPairs;
	    function tailPinNumberMapFromContext(ctx) {
	        const scanWork = ctx.extra?.symbolScanWork;
	        const bytes = ctx.bytes;
	        const offset = ctx.offset;
	        const byteLength = ctx.byteLength ?? bytes.length - offset;
	        if (byteLength < 10) {
	            return undefined;
	        }
	        let cursor = offset;
	        let packageName;
	        for (let candidateOffset = offset; candidateOffset < Math.min(offset + 16, offset + byteLength); candidateOffset++) {
	            if (scanWork) {
	                scanWork.mapStringProbes++;
	            }
	            const candidate = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, candidateOffset, {
	                maxLength: 256,
	            });
	            if (candidate) {
	                packageName = candidate;
	                cursor = candidateOffset;
	                break;
	            }
	        }
	        if (!packageName) {
	            return undefined;
	        }
	        cursor += 2 + packageName.length + (packageName.hasNullTerminator ? 1 : 0);
	        if (cursor + 2 > offset + byteLength) {
	            return undefined;
	        }
	        const count = readUInt16LE(bytes, cursor);
	        cursor += 2;
	        if (count < 1 || count > 4096) {
	            return undefined;
	        }
	        const strings = [packageName];
	        const entries = [];
	        for (let position = 0; position < count && cursor < offset + byteLength; position++) {
	            if (scanWork) {
	                scanWork.mapStringProbes++;
	            }
	            let number = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength: 64 });
	            if (!number &&
	                cursor + 3 <= offset + byteLength &&
	                readUInt16LE(bytes, cursor) === 0 &&
	                bytes[cursor + 2] === 0) {
	                number = { offset: cursor, length: 0, value: '', hasNullTerminator: true };
	            }
	            if (!number) {
	                return undefined;
	            }
	            strings.push(number);
	            entries.push({ position, number: number.value });
	            cursor += 2 + number.length + (number.hasNullTerminator ? 1 : 0);
	            if (cursor < offset + byteLength && bytes[cursor] === 0x7f) {
	                cursor++;
	            }
	        }
	        if (entries.length !== count) {
	            return undefined;
	        }
	        if (!entries.some((entry) => entry.number.length > 0)) {
	            return undefined;
	        }
	        const consumedByteLength = cursor - packageName.offset;
	        return {
	            offset: packageName.offset,
	            byteLength: consumedByteLength,
	            payloadByteLength: consumedByteLength,
	            packageName: packageName.value,
	            entries,
	            strings,
	        };
	    }
	    const primitiveBitmapRecordSpec = {
	        name: 'primitive.bitmap',
	        family: 'primitive',
	        kind: 'bitmap',
	        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2f, 0x2f])],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 4 },
	            locX: { kind: 'i32le', offset: 10 },
	            locY: { kind: 'i32le', offset: 14 },
	            x2: { kind: 'i32le', offset: 18 },
	            y2: { kind: 'i32le', offset: 22 },
	            x1: { kind: 'i32le', offset: 26 },
	            y1: { kind: 'i32le', offset: 30 },
	            pixelWidth: { kind: 'i32le', offset: 34 },
	            pixelHeight: { kind: 'i32le', offset: 38 },
	            dibByteLength: { kind: 'u32le', offset: 42 },
	            dibHeaderSize: { kind: 'u32le', offset: 46 },
	            dibPayload: {
	                kind: 'bytes',
	                offset: 46,
	                length: (ctx) => (typeof ctx.fields?.dibByteLength === 'number' ? ctx.fields.dibByteLength : undefined),
	                optional: true,
	            },
	        },
	        derive: function materializeBitmapPrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if (!hasBytes(bytes, offset, [0x2f, 0x2f]) || offset + 54 > bytes.length) {
	                return undefined;
	            }
	            const dibByteLength = numberField(fields, 'dibByteLength');
	            const dibHeaderSize = numberField(fields, 'dibHeaderSize');
	            const dibOffset = offset + 46;
	            const terminatorOffset = dibOffset + (dibByteLength ?? 0);
	            if (dibByteLength === undefined ||
	                dibHeaderSize === undefined ||
	                dibByteLength < 1 ||
	                ![12, 40, 108, 124].includes(dibHeaderSize) ||
	                dibByteLength > bytes.length - dibOffset - RECORD_TERMINATOR.length ||
	                !hasRecordTerminator(bytes, terminatorOffset)) {
	                return undefined;
	            }
	            const locX = numberField(fields, 'locX');
	            const locY = numberField(fields, 'locY');
	            const x2 = numberField(fields, 'x2');
	            const y2 = numberField(fields, 'y2');
	            const x1 = numberField(fields, 'x1');
	            const y1 = numberField(fields, 'y1');
	            const pixelWidth = numberField(fields, 'pixelWidth');
	            const pixelHeight = numberField(fields, 'pixelHeight');
	            if (locX === undefined ||
	                locY === undefined ||
	                x1 === undefined ||
	                y1 === undefined ||
	                x2 === undefined ||
	                y2 === undefined ||
	                pixelWidth === undefined ||
	                pixelHeight === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([locX, locY, x1, y1, x2, y2, pixelWidth, pixelHeight])) {
	                return undefined;
	            }
	            return {
	                kind: 'bitmap',
	                offset,
	                byteLength: terminatorOffset + RECORD_TERMINATOR.length - offset,
	                opcode: bytesToHex(bytes, offset, offset + 4),
	                ...(offset >= 73 ? { dbId: readUInt32LE(bytes, offset - 73) } : {}),
	                locX,
	                locY,
	                x1,
	                y1,
	                x2,
	                y2,
	                pixelWidth,
	                pixelHeight,
	                dibByteLength,
	                dibPayload: bytes.slice(dibOffset, terminatorOffset),
	            };
	        },
	    };
	    const primitiveCommentTextRecordSpec = {
	        name: 'primitive.comment-text',
	        family: 'primitive',
	        kind: 'comment-text',
	        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2e, 0x2e])],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            x1: { kind: 'i32le', offset: 10 },
	            y1: { kind: 'i32le', offset: 14 },
	            x2: { kind: 'i32le', offset: 18 },
	            y2: { kind: 'i32le', offset: 22 },
	            locX: { kind: 'i32le', offset: 26 },
	            locY: { kind: 'i32le', offset: 30 },
	            textStyleId: { kind: 'u16le', offset: 34 },
	            textFlags: { kind: 'u16le', offset: 36 },
	            nameLength: { kind: 'u16le', offset: 38 },
	            name: {
	                kind: 'ascii',
	                offset: 40,
	                length: (ctx) => (typeof ctx.fields?.nameLength === 'number' ? ctx.fields.nameLength : undefined),
	            },
	        },
	        derive: function materializeCommentTextPrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if (offset + 49 > bytes.length || bytes[offset] !== 0x2e || bytes[offset + 1] !== 0x2e) {
	                return undefined;
	            }
	            const nameLength = numberField(fields, 'nameLength');
	            const name = stringField(fields, 'name');
	            const nameStart = offset + 40;
	            const nameEnd = nameStart + (nameLength ?? 0);
	            const terminatorOffset = nameEnd + 1;
	            if (nameLength === undefined ||
	                name === undefined ||
	                nameLength < 1 ||
	                nameLength > 4096 ||
	                terminatorOffset > bytes.length ||
	                bytes[nameEnd] !== 0x00) {
	                return undefined;
	            }
	            const hasTerminator = terminatorOffset + RECORD_TERMINATOR.length <= bytes.length && hasRecordTerminator(bytes, terminatorOffset);
	            const x1 = numberField(fields, 'x1');
	            const y1 = numberField(fields, 'y1');
	            const x2 = numberField(fields, 'x2');
	            const y2 = numberField(fields, 'y2');
	            const locX = numberField(fields, 'locX');
	            const locY = numberField(fields, 'locY');
	            const textStyleId = numberField(fields, 'textStyleId');
	            const textFlags = numberField(fields, 'textFlags');
	            if (x1 === undefined ||
	                y1 === undefined ||
	                x2 === undefined ||
	                y2 === undefined ||
	                locX === undefined ||
	                locY === undefined ||
	                textStyleId === undefined ||
	                textFlags === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([x1, y1, x2, y2, locX, locY])) {
	                return undefined;
	            }
	            return {
	                kind: 'comment-text',
	                offset,
	                byteLength: terminatorOffset + (hasTerminator ? RECORD_TERMINATOR.length : 0) - offset,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                name,
	                x1,
	                y1,
	                x2,
	                y2,
	                locX,
	                locY,
	                textStyleId,
	                textFlags,
	            };
	        },
	    };
	    const primitiveRectRecordSpec = {
	        name: 'primitive.rect',
	        family: 'primitive',
	        kind: 'rect',
	        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x28, 0x28, 0x28])],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            x1: { kind: 'i32le', offset: 10 },
	            y1: { kind: 'i32le', offset: 14 },
	            x2: { kind: 'i32le', offset: 18 },
	            y2: { kind: 'i32le', offset: 22 },
	            lineStyle: { kind: 'i32le', offset: 26 },
	            lineWidth: { kind: 'i32le', offset: 30 },
	            fillStyle: { kind: 'i32le', offset: 34 },
	            hatchStyle: { kind: 'i32le', offset: 38 },
	        },
	        derive: function materializeRectPrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if (!hasBytes(bytes, offset, [0x28, 0x28, 0x28]) || !hasRecordTerminator(bytes, offset + 42)) {
	                return undefined;
	            }
	            const x1 = numberField(fields, 'x1');
	            const y1 = numberField(fields, 'y1');
	            const x2 = numberField(fields, 'x2');
	            const y2 = numberField(fields, 'y2');
	            const lineStyle = numberField(fields, 'lineStyle');
	            const lineWidth = numberField(fields, 'lineWidth');
	            const fillStyle = numberField(fields, 'fillStyle');
	            const hatchStyle = numberField(fields, 'hatchStyle');
	            if (x1 === undefined ||
	                y1 === undefined ||
	                x2 === undefined ||
	                y2 === undefined ||
	                lineStyle === undefined ||
	                lineWidth === undefined ||
	                fillStyle === undefined ||
	                hatchStyle === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([x1, y1, x2, y2])) {
	                return undefined;
	            }
	            return {
	                kind: 'rect',
	                offset,
	                byteLength: 50,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                x1,
	                y1,
	                x2,
	                y2,
	                lineStyle,
	                lineWidth,
	                fillStyle,
	                hatchStyle,
	            };
	        },
	    };
	    const primitiveEllipseRecordSpec = {
	        name: 'primitive.ellipse',
	        family: 'primitive',
	        kind: 'ellipse',
	        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2b, 0x2b, 0x28])],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            x1: { kind: 'i32le', offset: 10 },
	            y1: { kind: 'i32le', offset: 14 },
	            x2: { kind: 'i32le', offset: 18 },
	            y2: { kind: 'i32le', offset: 22 },
	            lineStyle: { kind: 'i32le', offset: 26 },
	            lineWidth: { kind: 'i32le', offset: 30 },
	            fillStyle: { kind: 'i32le', offset: 34 },
	            hatchStyle: { kind: 'i32le', offset: 38 },
	        },
	        derive: function materializeEllipsePrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if (!hasBytes(bytes, offset, [0x2b, 0x2b, 0x28]) || !hasRecordTerminator(bytes, offset + 42)) {
	                return undefined;
	            }
	            const x1 = numberField(fields, 'x1');
	            const y1 = numberField(fields, 'y1');
	            const x2 = numberField(fields, 'x2');
	            const y2 = numberField(fields, 'y2');
	            const lineStyle = numberField(fields, 'lineStyle');
	            const lineWidth = numberField(fields, 'lineWidth');
	            const fillStyle = numberField(fields, 'fillStyle');
	            const hatchStyle = numberField(fields, 'hatchStyle');
	            if (x1 === undefined ||
	                y1 === undefined ||
	                x2 === undefined ||
	                y2 === undefined ||
	                lineStyle === undefined ||
	                lineWidth === undefined ||
	                fillStyle === undefined ||
	                hatchStyle === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([x1, y1, x2, y2])) {
	                return undefined;
	            }
	            return {
	                kind: 'ellipse',
	                offset,
	                byteLength: 50,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                x1,
	                y1,
	                x2,
	                y2,
	                lineStyle,
	                lineWidth,
	                fillStyle,
	                hatchStyle,
	            };
	        },
	    };
	    const primitivePolygonRecordSpec = {
	        name: 'primitive.polygon',
	        family: 'primitive',
	        kind: 'polygon',
	        match: [
	            (ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2c, 0x2c, 0x3a]) ||
	                hasBytes(ctx.bytes, ctx.offset, [0x2c, 0x2c, 0x2e]) ||
	                hasBytes(ctx.bytes, ctx.offset, [0x2c, 0x2c, 0x2a]),
	        ],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            fillStyle: { kind: 'i32le', offset: 10 },
	            hatchStyle: { kind: 'i32le', offset: 14 },
	            lineStyle: { kind: 'i32le', offset: 18 },
	            lineWidth: { kind: 'i32le', offset: 22 },
	            pointCount: { kind: 'u16le', offset: 26 },
	            points: {
	                kind: 'repeated',
	                offset: 28,
	                count: (ctx) => (typeof ctx.fields?.pointCount === 'number' ? ctx.fields.pointCount : undefined),
	                byteLength: 4,
	                fields: {
	                    y: { kind: 'i16le', offset: 0 },
	                    x: { kind: 'i16le', offset: 2 },
	                },
	            },
	        },
	        derive: function materializePolygonPrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if ((!hasBytes(bytes, offset, [0x2c, 0x2c, 0x3a]) &&
	                !hasBytes(bytes, offset, [0x2c, 0x2c, 0x2e]) &&
	                !hasBytes(bytes, offset, [0x2c, 0x2c, 0x2a])) ||
	                offset + 28 > bytes.length) {
	                return undefined;
	            }
	            const pointCount = numberField(fields, 'pointCount');
	            const pointsOffset = offset + 28;
	            const terminatorOffset = pointsOffset + (pointCount ?? 0) * 4;
	            if (pointCount === undefined ||
	                pointCount < 2 ||
	                pointCount > 4096 ||
	                !hasRecordTerminator(bytes, terminatorOffset)) {
	                return undefined;
	            }
	            const points = fields.points;
	            const fillStyle = numberField(fields, 'fillStyle');
	            const hatchStyle = numberField(fields, 'hatchStyle');
	            const lineStyle = numberField(fields, 'lineStyle');
	            const lineWidth = numberField(fields, 'lineWidth');
	            if (!points ||
	                fillStyle === undefined ||
	                hatchStyle === undefined ||
	                lineStyle === undefined ||
	                lineWidth === undefined) {
	                return undefined;
	            }
	            const normalizedPoints = points.map((point) => ({
	                x: typeof point.x === 'number' ? point.x : Number.NaN,
	                y: typeof point.y === 'number' ? point.y : Number.NaN,
	            }));
	            if (!normalizedPoints.every((point) => arePlausibleCoordinates([point.x, point.y]))) {
	                return undefined;
	            }
	            if (hasBytes(bytes, offset, [0x2c, 0x2c, 0x2a]) &&
	                (pointCount !== 4 || [fillStyle, hatchStyle, lineStyle, lineWidth].some((value) => value !== 0))) {
	                return undefined;
	            }
	            const isCompact = hasBytes(bytes, offset, [0x2c, 0x2c, 0x2e]);
	            return {
	                kind: 'polygon',
	                offset,
	                byteLength: terminatorOffset + RECORD_TERMINATOR.length - offset,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                fillStyle: isCompact ? lineStyle : fillStyle,
	                hatchStyle,
	                lineStyle: isCompact ? fillStyle : lineStyle,
	                lineWidth,
	                points: normalizedPoints,
	            };
	        },
	    };
	    const POLYLINE_MIN_POINTS = 2;
	    const POLYLINE_MAX_POINTS = 4096;
	    function polylineFramingAt(bytes, offset) {
	        if (!hasBytes(bytes, offset, [0x2d, 0x2d]) || offset + 20 > bytes.length) {
	            return undefined;
	        }
	        const pointCount = readUInt16LE(bytes, offset + 18);
	        if (pointCount < POLYLINE_MIN_POINTS || pointCount > POLYLINE_MAX_POINTS) {
	            return undefined;
	        }
	        const terminatorOffset = offset + 20 + pointCount * 4;
	        const byteLength = terminatorOffset + RECORD_TERMINATOR.length - offset;
	        if (byteLength !== bytes[offset + 2] + 10 || !hasRecordTerminator(bytes, terminatorOffset)) {
	            return undefined;
	        }
	        return { pointCount, byteLength };
	    }
	    const primitivePolylineRecordSpec = {
	        name: 'primitive.polyline',
	        family: 'primitive',
	        kind: 'polyline',
	        match: [(ctx) => polylineFramingAt(ctx.bytes, ctx.offset) !== undefined],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            declaredLength: { kind: 'u8', offset: 2 },
	            lineStyle: { kind: 'i32le', offset: 10 },
	            lineWidth: { kind: 'i32le', offset: 14 },
	            pointCount: { kind: 'u16le', offset: 18 },
	            points: {
	                kind: 'repeated',
	                offset: 20,
	                count: (ctx) => polylineFramingAt(ctx.bytes, ctx.offset)?.pointCount,
	                byteLength: 4,
	                fields: {
	                    y: { kind: 'i16le', offset: 0 },
	                    x: { kind: 'i16le', offset: 2 },
	                },
	            },
	        },
	        derive: function materializePolylinePrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            const framing = polylineFramingAt(bytes, offset);
	            if (!framing) {
	                return undefined;
	            }
	            const { byteLength } = framing;
	            const points = fields.points;
	            const lineStyle = numberField(fields, 'lineStyle');
	            const lineWidth = numberField(fields, 'lineWidth');
	            if (!points || lineStyle === undefined || lineWidth === undefined) {
	                return undefined;
	            }
	            const normalizedPoints = points.map((point) => ({
	                x: typeof point.x === 'number' ? point.x : Number.NaN,
	                y: typeof point.y === 'number' ? point.y : Number.NaN,
	            }));
	            if (!normalizedPoints.every((point) => arePlausibleCoordinates([point.x, point.y]))) {
	                return undefined;
	            }
	            return {
	                kind: 'polyline',
	                offset,
	                byteLength,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                lineStyle,
	                lineWidth,
	                points: normalizedPoints,
	            };
	        },
	    };
	    const primitiveArcRecordSpec = {
	        name: 'primitive.arc',
	        family: 'primitive',
	        kind: 'arc',
	        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2a, 0x2a, 0x30])],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            x1: { kind: 'i32le', offset: 10 },
	            y1: { kind: 'i32le', offset: 14 },
	            x2: { kind: 'i32le', offset: 18 },
	            y2: { kind: 'i32le', offset: 22 },
	            startX: { kind: 'i32le', offset: 26 },
	            startY: { kind: 'i32le', offset: 30 },
	            endX: { kind: 'i32le', offset: 34 },
	            endY: { kind: 'i32le', offset: 38 },
	            lineStyle: { kind: 'i32le', offset: 42 },
	            lineWidth: { kind: 'i32le', offset: 46 },
	        },
	        derive: function materializeArcPrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if (!hasBytes(bytes, offset, [0x2a, 0x2a, 0x30]) || !hasRecordTerminator(bytes, offset + 50)) {
	                return undefined;
	            }
	            const x1 = numberField(fields, 'x1');
	            const y1 = numberField(fields, 'y1');
	            const x2 = numberField(fields, 'x2');
	            const y2 = numberField(fields, 'y2');
	            const startX = numberField(fields, 'startX');
	            const startY = numberField(fields, 'startY');
	            const endX = numberField(fields, 'endX');
	            const endY = numberField(fields, 'endY');
	            const lineStyle = numberField(fields, 'lineStyle');
	            const lineWidth = numberField(fields, 'lineWidth');
	            if (x1 === undefined ||
	                y1 === undefined ||
	                x2 === undefined ||
	                y2 === undefined ||
	                startX === undefined ||
	                startY === undefined ||
	                endX === undefined ||
	                endY === undefined ||
	                lineStyle === undefined ||
	                lineWidth === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([x1, y1, x2, y2, startX, startY, endX, endY])) {
	                return undefined;
	            }
	            return {
	                kind: 'arc',
	                offset,
	                byteLength: 58,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                x1,
	                y1,
	                x2,
	                y2,
	                startX,
	                startY,
	                endX,
	                endY,
	                lineStyle,
	                lineWidth,
	            };
	        },
	    };
	    const primitiveLineRecordSpec = {
	        name: 'primitive.line',
	        family: 'primitive',
	        kind: 'line',
	        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x29, 0x29, 0x20])],
	        fields: {
	            opcode: { kind: 'bytes', offset: 0, length: 3 },
	            x1: { kind: 'i32le', offset: 10 },
	            y1: { kind: 'i32le', offset: 14 },
	            x2: { kind: 'i32le', offset: 18 },
	            y2: { kind: 'i32le', offset: 22 },
	            lineStyle: { kind: 'i32le', offset: 26 },
	            lineWidth: { kind: 'i32le', offset: 30 },
	        },
	        derive: function materializeLinePrimitive(fields, ctx) {
	            const { bytes, offset } = ctx;
	            if (!hasBytes(bytes, offset, [0x29, 0x29, 0x20]) || !hasRecordTerminator(bytes, offset + 34)) {
	                return undefined;
	            }
	            const x1 = numberField(fields, 'x1');
	            const y1 = numberField(fields, 'y1');
	            const x2 = numberField(fields, 'x2');
	            const y2 = numberField(fields, 'y2');
	            const lineStyle = numberField(fields, 'lineStyle');
	            const lineWidth = numberField(fields, 'lineWidth');
	            if (x1 === undefined ||
	                y1 === undefined ||
	                x2 === undefined ||
	                y2 === undefined ||
	                lineStyle === undefined ||
	                lineWidth === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([x1, y1, x2, y2])) {
	                return undefined;
	            }
	            return {
	                kind: 'line',
	                offset,
	                byteLength: 42,
	                opcode: bytesToHex(bytes, offset, offset + 3),
	                x1,
	                y1,
	                x2,
	                y2,
	                lineStyle,
	                lineWidth,
	            };
	        },
	    };
	    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PRIMITIVE_RECORD_SPECS = [
	        primitiveBitmapRecordSpec,
	        primitiveCommentTextRecordSpec,
	        primitiveRectRecordSpec,
	        primitiveEllipseRecordSpec,
	        primitivePolygonRecordSpec,
	        primitivePolylineRecordSpec,
	        primitiveArcRecordSpec,
	        primitiveLineRecordSpec,
	    ];
	    const pageWireRecordSpec = {
	        name: 'page.wire',
	        family: 'page',
	        kind: 'wire',
	        match: [(ctx) => ctx.bytes.length >= 52 && ctx.bytes.length <= 2048],
	        fields: {
	            dbId: { kind: 'u32le', offset: 0 },
	            metadataId: { kind: 'u32le', offset: 4 },
	            color: { kind: 'i32le', offset: 8 },
	            startX: { kind: 'i32le', offset: 12 },
	            startY: { kind: 'i32le', offset: 16 },
	            endX: { kind: 'i32le', offset: 20 },
	            endY: { kind: 'i32le', offset: 24 },
	            childMarker: { kind: 'u32le', offset: 28 },
	            lineWidth: { kind: 'u8', offset: 33, optional: true },
	            lineStyle: { kind: 'u8', offset: 37, optional: true },
	            wireType: { kind: 'u8', offset: 41, optional: true },
	        },
	        derive: function materializeWirePageObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (bytes.length < 52 || bytes.length > 2048) {
	                return undefined;
	            }
	            const dbId = numberField(fields, 'dbId');
	            const metadataId = numberField(fields, 'metadataId');
	            const color = numberField(fields, 'color');
	            const startX = numberField(fields, 'startX');
	            const startY = numberField(fields, 'startY');
	            const endX = numberField(fields, 'endX');
	            const endY = numberField(fields, 'endY');
	            const childMarker = numberField(fields, 'childMarker');
	            const lineWidth = numberField(fields, 'lineWidth');
	            const lineStyle = numberField(fields, 'lineStyle');
	            const wireType = numberField(fields, 'wireType');
	            if (dbId === undefined ||
	                metadataId === undefined ||
	                color === undefined ||
	                startX === undefined ||
	                startY === undefined ||
	                endX === undefined ||
	                endY === undefined ||
	                childMarker === undefined ||
	                dbId <= 0 ||
	                dbId > 1_000_000_000 ||
	                metadataId <= 0 ||
	                metadataId > 1_000_000_000 ||
	                color < 0 ||
	                color > 255 ||
	                !arePlausibleCoordinates([startX, startY, endX, endY])) {
	                return undefined;
	            }
	            const junctions = wireJunctionsFromMarker(childMarker, startX, startY, endX, endY);
	            return {
	                kind: 'wire',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                dbId,
	                metadataId,
	                color,
	                startX,
	                startY,
	                endX,
	                endY,
	                childMarker,
	                ...(wireType !== undefined && wireType <= 255 ? { wireType } : {}),
	                ...(junctions.length > 0 ? { junctions } : {}),
	                ...((bytes.length !== 52 &&
	                    lineWidth !== undefined &&
	                    lineStyle !== undefined &&
	                    lineWidth <= 20 &&
	                    lineStyle <= 20) ?
	                    { lineWidth, lineStyle }
	                    : {}),
	            };
	        },
	    };
	    const BUS_ENTRY_RECORD_MARKER = 0x30;
	    const pageBusEntryRecordSpec = {
	        name: 'page.bus-entry',
	        family: 'page',
	        kind: 'bus-entry',
	        match: [
	            (ctx) => ctx.bytes.length >= 20 && ctx.bytes.length <= 64,
	            (ctx) => readUInt32LE(ctx.bytes, 0) === BUS_ENTRY_RECORD_MARKER,
	            (ctx) => {
	                const dx = Math.abs(readInt32LE(ctx.bytes, 4) - readInt32LE(ctx.bytes, 12));
	                const dy = Math.abs(readInt32LE(ctx.bytes, 8) - readInt32LE(ctx.bytes, 16));
	                return dx > 0 && dx === dy;
	            },
	        ],
	        fields: {
	            busX: { kind: 'i32le', offset: 4 },
	            busY: { kind: 'i32le', offset: 8 },
	            endX: { kind: 'i32le', offset: 12 },
	            endY: { kind: 'i32le', offset: 16 },
	        },
	        derive: function materializeBusEntryPageObject(fields) {
	            const busX = fields.busX;
	            const busY = fields.busY;
	            const endX = fields.endX;
	            const endY = fields.endY;
	            if (typeof busX !== 'number' ||
	                typeof busY !== 'number' ||
	                typeof endX !== 'number' ||
	                typeof endY !== 'number' ||
	                !arePlausibleCoordinates([busX, busY, endX, endY])) {
	                return undefined;
	            }
	            return { kind: 'bus-entry', offset: 0, byteLength: 0, payloadByteLength: 0, busX, busY, endX, endY };
	        },
	    };
	    const DRAWN_PORT_REQUIRED_STYLE_FLAGS = 0x21;
	    const DRAWN_PORT_KNOWN_STYLE_FLAGS = 0x79;
	    const DRAWN_PORT_NET_STYLE_FLAG = 0x40;
	    const pageDrawnPortRecordSpec = {
	        name: 'page.drawn-port',
	        family: 'page',
	        kind: 'drawn-port',
	        match: [(ctx) => ctx.bytes.length >= 48 && ctx.bytes.length <= 192],
	        fields: {
	            name: { kind: 'counted-ascii', offset: 0, maxLength: 256 },
	            dataOffset: {
	                kind: 'custom',
	                offset: 0,
	                read: leadingCountedStringEndFromContext,
	            },
	            startX: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset : undefined),
	            },
	            startY: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 4 : undefined),
	            },
	            hotptX: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 8 : undefined),
	            },
	            hotptY: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 12 : undefined),
	            },
	            styleFlags: {
	                kind: 'u8',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 16 : undefined),
	            },
	            type: {
	                kind: 'u16le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 20 : undefined),
	                optional: true,
	            },
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 4, maxLength: 256 }),
	            },
	            asciiRuns: { kind: 'ascii-runs', offset: 0, minLength: 4, maxCount: 8 },
	        },
	        derive: function materializeDrawnPortPageObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (bytes.length < 48 || bytes.length > 192) {
	                return undefined;
	            }
	            const nameValue = stringField(fields, 'name');
	            if (!nameValue) {
	                return undefined;
	            }
	            const dataOffset = numberField(fields, 'dataOffset');
	            if (dataOffset === undefined) {
	                return undefined;
	            }
	            if (dataOffset + 25 > bytes.length) {
	                return undefined;
	            }
	            const startX = numberField(fields, 'startX');
	            const startY = numberField(fields, 'startY');
	            const hotptX = numberField(fields, 'hotptX');
	            const hotptY = numberField(fields, 'hotptY');
	            const styleFlags = numberField(fields, 'styleFlags');
	            if (startX === undefined ||
	                startY === undefined ||
	                hotptX === undefined ||
	                hotptY === undefined ||
	                styleFlags === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([startX, startY, hotptX, hotptY])) {
	                return undefined;
	            }
	            if ((styleFlags & DRAWN_PORT_REQUIRED_STYLE_FLAGS) !== DRAWN_PORT_REQUIRED_STYLE_FLAGS ||
	                (styleFlags & ~DRAWN_PORT_KNOWN_STYLE_FLAGS) !== 0) {
	                placedSymbolDecodeExtras(ctx).diagnostics?.push({
	                    severity: 'info',
	                    code: 'drawn-port-style-flags-unknown',
	                    message: `a sheet-symbol pin record named "${nameValue}" carries style flags 0x${styleFlags.toString(16)}, which is no combination the format is known to write`,
	                    streamPath: ctx.streamPath,
	                    offset: record.offset,
	                });
	                return undefined;
	            }
	            const type = numberField(fields, 'type');
	            const ownerName = stringArrayField(fields, 'countedStrings').find((string) => string.offset > dataOffset + 32);
	            return {
	                kind: 'drawn-port',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                name: nameValue,
	                startX,
	                startY,
	                hotptX,
	                hotptY,
	                ...(type !== undefined && type <= 32 ? { type } : {}),
	                visible: true,
	                isNetStyle: (styleFlags & DRAWN_PORT_NET_STYLE_FLAG) !== 0,
	                isBus: nameValue.includes('['),
	                ...(ownerName ? { ownerName: ownerName.value } : {}),
	            };
	        },
	    };
	    const pageAliasRecordSpec = {
	        name: 'page.alias',
	        family: 'page',
	        kind: 'alias',
	        match: [(ctx) => ctx.bytes.length >= 32],
	        fields: {
	            locX: { kind: 'i32le', offset: 0 },
	            locY: { kind: 'i32le', offset: 4 },
	            color: { kind: 'i32le', offset: 8 },
	            rotation: { kind: 'i32le', offset: 12 },
	            name: {
	                kind: 'counted-ascii',
	                offset: 20,
	                maxLength: 512,
	            },
	        },
	        derive: function materializeAliasPageObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (bytes.length < 32) {
	                return undefined;
	            }
	            const locX = numberField(fields, 'locX');
	            const locY = numberField(fields, 'locY');
	            const color = numberField(fields, 'color');
	            const rotation = numberField(fields, 'rotation');
	            const name = stringField(fields, 'name');
	            if (locX === undefined ||
	                locY === undefined ||
	                color === undefined ||
	                rotation === undefined ||
	                name === undefined ||
	                color < 0 ||
	                color > 255 ||
	                rotation < 0 ||
	                rotation > 3 ||
	                !arePlausibleCoordinates([locX, locY])) {
	                return undefined;
	            }
	            return {
	                kind: 'alias',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                name,
	                locX,
	                locY,
	                color,
	                rotation,
	            };
	        },
	    };
	    const pagePlacedSymbolRecordSpec = {
	        name: 'page.placed-symbol',
	        family: 'page',
	        kind: 'placed-symbol',
	        match: [(ctx) => ctx.bytes.length >= 32 && ctx.bytes.length <= 512],
	        fields: {
	            recordType: { kind: 'u32le', offset: 0 },
	            marker: { kind: 'u32le', offset: 4, optional: true },
	            candidates: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => placedSymbolCandidatesFromContext(ctx),
	            },
	            strings: { kind: 'ascii-runs', offset: 0, minLength: 4, maxCount: 16 },
	        },
	        derive: function materializePlacedSymbolPageObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const candidate = placedSymbolCandidateField(fields);
	            if (!candidate) {
	                return undefined;
	            }
	            const { displayCountOffset, ...semanticCandidate } = candidate;
	            placedSymbolDecodeExtras(ctx).placementDisplayCounts?.set(record.offset, displayCountOffset);
	            return {
	                kind: 'placed-symbol',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                ...semanticCandidate,
	            };
	        },
	    };
	    const pageDisplayPropertyRecordSpec = {
	        name: 'page.display-property',
	        family: 'page',
	        kind: 'display-property',
	        match: [(ctx) => ctx.bytes.length >= 20],
	        fields: {
	            propertyCode: { kind: 'u32le', offset: 0 },
	            locX: { kind: 'i16le', offset: 4 },
	            locY: { kind: 'i16le', offset: 6 },
	            rawFlags: { kind: 'u16le', offset: 8 },
	            color: { kind: 'u16le', offset: 10, optional: true },
	            colorByte: { kind: 'u8', offset: 10, optional: true },
	            textStyleId: { kind: 'u16le', offset: 12, optional: true },
	            displayType: { kind: 'u16le', offset: 12, optional: true },
	            marker: { kind: 'u8', offset: 14, optional: true },
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 8, maxLength: 256, requireNullTerminator: false }),
	            },
	            displayedValueCandidates: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 8 },
	        },
	        confidence: 'heuristic',
	        derive: function materializeDisplayPropertyPageObject(fields, ctx) {
	            function materializeCompactDisplayPropertyPageObject(fields, record) {
	                const bytes = record.payload;
	                if (bytes.length < 20 || bytes.length > 80) {
	                    return undefined;
	                }
	                const propertyCode = numberField(fields, 'propertyCode');
	                const locX = numberField(fields, 'locX');
	                const locY = numberField(fields, 'locY');
	                const rawFlags = numberField(fields, 'rawFlags');
	                const color = numberField(fields, 'colorByte');
	                const textStyleId = numberField(fields, 'textStyleId');
	                const marker = numberField(fields, 'marker');
	                if (propertyCode === undefined ||
	                    locX === undefined ||
	                    locY === undefined ||
	                    rawFlags === undefined ||
	                    color === undefined ||
	                    textStyleId === undefined ||
	                    marker === undefined ||
	                    propertyCode < 1 ||
	                    propertyCode > 0xffff ||
	                    ![0x18, 0x21, 0x23, 0x27].includes(marker) ||
	                    color > 255 ||
	                    textStyleId > 4096 ||
	                    !arePlausibleCoordinates([locX, locY])) {
	                    return undefined;
	                }
	                const displayedValue = stringArrayField(fields, 'countedStrings').find((string) => string.offset >= 14 &&
	                    string.value.trim().length > 0 &&
	                    (typeof ctx.extra?.displayFrameEnd !== 'number' ||
	                        record.offset + string.offset + 2 + string.length + (string.hasNullTerminator ? 1 : 0) <=
	                            ctx.extra.displayFrameEnd));
	                return {
	                    kind: 'display-property',
	                    offset: record.offset,
	                    byteLength: record.byteLength,
	                    payloadByteLength: record.payloadByteLength,
	                    propertyCode,
	                    locX,
	                    locY,
	                    rotation: rawFlags & 0x03,
	                    color,
	                    displayType: rawFlags,
	                    textStyleId,
	                    rawFlags,
	                    recordFormat: 'compact',
	                    ...(displayedValue ? { displayedValue: displayedValue.value } : {}),
	                    ...(displayedValue?.followingUInt32Le !== undefined ?
	                        { libraryStringIndex: displayedValue.followingUInt32Le }
	                        : {}),
	                };
	            }
	            function materializeCompositeDisplayPropertyPageObject(fields, record) {
	                const bytes = record.payload;
	                if (bytes.length < 512) {
	                    return undefined;
	                }
	                const propertyCode = numberField(fields, 'propertyCode');
	                if (propertyCode !== 0x32fb && propertyCode !== 0x3bdd) {
	                    return undefined;
	                }
	                const locX = numberField(fields, 'locX');
	                const locY = numberField(fields, 'locY');
	                const rawFlags = numberField(fields, 'rawFlags');
	                const color = numberField(fields, 'colorByte');
	                const textStyleId = numberField(fields, 'textStyleId');
	                const marker = numberField(fields, 'marker');
	                if (locX === undefined ||
	                    locY === undefined ||
	                    rawFlags === undefined ||
	                    color === undefined ||
	                    textStyleId === undefined ||
	                    marker !== 0x40 ||
	                    color > 255 ||
	                    textStyleId > 4096 ||
	                    !arePlausibleCoordinates([locX, locY])) {
	                    return undefined;
	                }
	                return {
	                    kind: 'display-property',
	                    offset: record.offset,
	                    byteLength: record.byteLength,
	                    payloadByteLength: record.payloadByteLength,
	                    propertyCode,
	                    locX,
	                    locY,
	                    rotation: rawFlags & 0x03,
	                    color,
	                    displayType: rawFlags,
	                    textStyleId,
	                    rawFlags,
	                    recordFormat: 'composite',
	                };
	            }
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (bytes.length < 20 || bytes.length > 160) {
	                return materializeCompositeDisplayPropertyPageObject(fields, record);
	            }
	            const propertyCode = numberField(fields, 'propertyCode');
	            const locX = numberField(fields, 'locX');
	            const locY = numberField(fields, 'locY');
	            const rotation = numberField(fields, 'rawFlags');
	            const color = numberField(fields, 'color');
	            const displayType = numberField(fields, 'displayType');
	            if (propertyCode === undefined ||
	                locX === undefined ||
	                locY === undefined ||
	                rotation === undefined ||
	                color === undefined ||
	                displayType === undefined ||
	                propertyCode < 1 ||
	                propertyCode > 10_000 ||
	                rotation > 3 ||
	                color > 255 ||
	                displayType > 16 ||
	                !arePlausibleCoordinates([locX, locY])) {
	                return materializeCompactDisplayPropertyPageObject(fields, record);
	            }
	            if (!hasBytes(bytes, 14, [0x27, 0x22]) && bytes[14] !== 0x18) {
	                return materializeCompactDisplayPropertyPageObject(fields, record);
	            }
	            const displayedValue = stringArrayField(fields, 'countedStrings').find((string) => string.offset >= 14 &&
	                string.value.trim().length > 0 &&
	                (typeof ctx.extra?.displayFrameEnd !== 'number' ||
	                    record.offset + string.offset + 2 + string.length + (string.hasNullTerminator ? 1 : 0) <=
	                        ctx.extra.displayFrameEnd));
	            return {
	                kind: 'display-property',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                propertyCode,
	                locX,
	                locY,
	                rotation,
	                color,
	                displayType,
	                recordFormat: 'standard',
	                ...(displayedValue ? { displayedValue: displayedValue.value } : {}),
	                ...(displayedValue?.followingUInt32Le !== undefined ?
	                    { libraryStringIndex: displayedValue.followingUInt32Le }
	                    : {}),
	            };
	        },
	    };
	    const pagePinToWireRecordSpec = {
	        name: 'page.pin-to-wire',
	        family: 'connectivity',
	        kind: 'pin-to-wire',
	        match: [(ctx) => ctx.extra?.ownedConnectivityFrame === true && ctx.bytes.length >= 16],
	        fields: {
	            signedPinIndex: { kind: 'i16le', offset: 0 },
	            x: { kind: 'i16le', offset: 2 },
	            y: { kind: 'i16le', offset: 4 },
	            wireDbId: { kind: 'u32le', offset: 6 },
	            ownerName: { kind: 'counted-ascii', offset: 16, maxLength: 256, optional: true },
	        },
	        derive: function materializePinToWirePageObject(fields, ctx) {
	            function materializePortInstancePageObject(fields, ctx) {
	                const record = terminatedPayloadFromContext(ctx);
	                const bytes = record.payload;
	                if (ctx.extra?.ownedConnectivityFrame !== true || bytes.length < 16) {
	                    return undefined;
	                }
	                const signedPinIndex = numberField(fields, 'signedPinIndex');
	                const x = numberField(fields, 'x');
	                const y = numberField(fields, 'y');
	                const wireDbId = numberField(fields, 'wireDbId');
	                if (signedPinIndex === undefined || x === undefined || y === undefined || wireDbId === undefined) {
	                    return undefined;
	                }
	                const pinIndex = Math.abs(signedPinIndex);
	                const isNoConnect = signedPinIndex < 0 || wireDbId === 0;
	                if (pinIndex < 1 ||
	                    pinIndex > 4096 ||
	                    (wireDbId !== 0 && (wireDbId < 100_000 || wireDbId > 1_000_000_000)) ||
	                    !arePlausibleCoordinates([x, y])) {
	                    return undefined;
	                }
	                const ownerName = stringField(fields, 'ownerName');
	                return {
	                    kind: 'port-instance',
	                    offset: record.offset,
	                    byteLength: record.byteLength,
	                    payloadByteLength: record.payloadByteLength,
	                    pinIndex,
	                    position: pinIndex - 1,
	                    ...(signedPinIndex < 0 ? { pinName: String(pinIndex) } : {}),
	                    x,
	                    y,
	                    ...(wireDbId !== 0 ? { wireDbId } : {}),
	                    isNoConnect,
	                    source: 'record',
	                    ...(ownerName ? { ownerName } : {}),
	                };
	            }
	            const portInstance = materializePortInstancePageObject(fields, ctx);
	            if (!portInstance ||
	                portInstance.kind !== 'port-instance' ||
	                portInstance.isNoConnect ||
	                portInstance.wireDbId === undefined) {
	                return undefined;
	            }
	            return {
	                kind: 'pin-to-wire',
	                offset: portInstance.offset,
	                byteLength: portInstance.byteLength,
	                payloadByteLength: portInstance.payloadByteLength,
	                pinIndex: portInstance.pinIndex,
	                position: portInstance.position,
	                x: portInstance.x,
	                y: portInstance.y,
	                wireDbId: portInstance.wireDbId,
	                isNoConnect: false,
	                ...(portInstance.ownerName ? { ownerName: portInstance.ownerName } : {}),
	            };
	        },
	    };
	    const pagePortInstanceRecordSpec = {
	        name: 'page.port-instance',
	        family: 'connectivity',
	        kind: 'port-instance',
	        match: [(ctx) => ctx.extra?.ownedConnectivityFrame === true && ctx.bytes.length >= 16],
	        fields: {
	            signedPinIndex: { kind: 'i16le', offset: 0 },
	            x: { kind: 'i16le', offset: 2 },
	            y: { kind: 'i16le', offset: 4 },
	            wireDbId: { kind: 'u32le', offset: 6 },
	            ownerName: { kind: 'counted-ascii', offset: 16, maxLength: 256, optional: true },
	        },
	        derive: function materializePortInstancePageObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (ctx.extra?.ownedConnectivityFrame !== true || bytes.length < 16) {
	                return undefined;
	            }
	            const signedPinIndex = numberField(fields, 'signedPinIndex');
	            const x = numberField(fields, 'x');
	            const y = numberField(fields, 'y');
	            const wireDbId = numberField(fields, 'wireDbId');
	            if (signedPinIndex === undefined || x === undefined || y === undefined || wireDbId === undefined) {
	                return undefined;
	            }
	            const pinIndex = Math.abs(signedPinIndex);
	            const isNoConnect = signedPinIndex < 0 || wireDbId === 0;
	            if (pinIndex < 1 ||
	                pinIndex > 4096 ||
	                (wireDbId !== 0 && (wireDbId < 100_000 || wireDbId > 1_000_000_000)) ||
	                !arePlausibleCoordinates([x, y])) {
	                return undefined;
	            }
	            const ownerName = stringField(fields, 'ownerName');
	            return {
	                kind: 'port-instance',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                pinIndex,
	                position: pinIndex - 1,
	                ...(signedPinIndex < 0 ? { pinName: String(pinIndex) } : {}),
	                x,
	                y,
	                ...(wireDbId !== 0 ? { wireDbId } : {}),
	                isNoConnect,
	                source: 'record',
	                ...(ownerName ? { ownerName } : {}),
	            };
	        },
	    };
	    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS = [
	        pageWireRecordSpec,
	        pageDrawnPortRecordSpec,
	        pageAliasRecordSpec,
	        pagePlacedSymbolRecordSpec,
	        pageDisplayPropertyRecordSpec,
	        pagePinToWireRecordSpec,
	        pagePortInstanceRecordSpec,
	        pageBusEntryRecordSpec,
	    ];
	    const symbolPinNumberMapInlineRecordSpec = {
	        name: 'symbol.pin-number-map-inline',
	        family: 'symbol',
	        kind: 'pin-number-map',
	        match: [(ctx) => ctx.bytes.length >= 12],
	        fields: {
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 128, maxLength: 512 }),
	            },
	            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 128 },
	        },
	        derive: function materializeInlinePinNumberMapObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const strings = stringArrayField(fields, 'countedStrings');
	            if (strings.length < 3 || strings[0]?.offset !== 0) {
	                return undefined;
	            }
	            const packageName = strings.find((string, index) => index > 0 && (string.value === ctx.streamName || string.offset <= 8));
	            if (!packageName) {
	                return undefined;
	            }
	            const entries = strings
	                .slice(strings.indexOf(packageName) + 1)
	                .filter((string) => /^[A-Za-z0-9_.-]+$/.test(string.value))
	                .map((string, position) => ({ position, number: string.value }));
	            if (entries.length === 0) {
	                return undefined;
	            }
	            return {
	                kind: 'pin-number-map',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                selector: strings[0].value,
	                packageName: packageName.value,
	                entries,
	                strings,
	            };
	        },
	    };
	    const VIEW_HEADER_PRIMITIVE_COUNT_MARKER = 0x30;
	    function viewHeaderPrimitiveCountFromContext(ctx) {
	        const name = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(ctx.bytes, ctx.offset, { maxLength: 512 });
	        if (!name) {
	            return undefined;
	        }
	        const sourceLibrary = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(ctx.bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name), { maxLength: 512 });
	        if (!sourceLibrary) {
	            return undefined;
	        }
	        const markerOffset = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(sourceLibrary);
	        if (markerOffset + 6 > ctx.bytes.length ||
	            DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt32LE(ctx.bytes, markerOffset) !== VIEW_HEADER_PRIMITIVE_COUNT_MARKER) {
	            return undefined;
	        }
	        return readUInt16LE(ctx.bytes, markerOffset + 4);
	    }
	    const symbolViewHeaderRecordSpec = {
	        name: 'symbol.view-header',
	        family: 'symbol',
	        kind: 'view-header',
	        match: [(ctx) => ctx.bytes.length >= 4],
	        fields: {
	            name: { kind: 'counted-ascii', offset: 0, maxLength: 512, optional: true },
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 64, maxLength: 512 }),
	            },
	            primitiveCount: {
	                kind: 'custom',
	                offset: 0,
	                read: viewHeaderPrimitiveCountFromContext,
	            },
	            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 64 },
	        },
	        derive: function materializeSymbolViewHeaderObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const strings = stringArrayField(fields, 'countedStrings');
	            const first = strings[0];
	            if (!first || first.offset !== 0) {
	                return undefined;
	            }
	            const viewName = strings.find((string) => string.value.endsWith('.Normal') || string.value.endsWith('.Convert'));
	            if (!viewName && first.value === ctx.streamName) {
	                return undefined;
	            }
	            if (!isStreamViewName(first.value, ctx.streamName) && !viewName) {
	                return undefined;
	            }
	            const primitiveCount = numberField(fields, 'primitiveCount');
	            return {
	                kind: 'view-header',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                name: first.value,
	                ...(viewName ? { viewName: viewName.value } : {}),
	                ...(primitiveCount === undefined ? {} : { primitiveCount }),
	                strings,
	            };
	        },
	    };
	    const symbolPinRecordSpec = {
	        name: 'symbol.pin',
	        family: 'symbol',
	        kind: 'symbol-pin',
	        match: [(ctx) => ctx.bytes.length >= 24],
	        fields: {
	            name: { kind: 'counted-ascii', offset: 0, maxLength: 256, optional: true },
	            dataOffset: {
	                kind: 'custom',
	                offset: 0,
	                read: leadingCountedStringEndFromContext,
	            },
	            startX: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset : undefined),
	            },
	            startY: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 4 : undefined),
	            },
	            hotptX: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 8 : undefined),
	            },
	            hotptY: {
	                kind: 'i32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 12 : undefined),
	            },
	            displayFlags: {
	                kind: 'u16le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 16 : undefined),
	                optional: true,
	            },
	            pinType: {
	                kind: 'u32le',
	                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 20 : undefined),
	                optional: true,
	            },
	            asciiRuns: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 8 },
	        },
	        derive: function materializeSymbolPinObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (bytes.length < 24) {
	                return undefined;
	            }
	            const nameValue = stringField(fields, 'name');
	            if (!nameValue || isStreamViewName(nameValue, ctx.streamName)) {
	                return undefined;
	            }
	            const dataOffset = numberField(fields, 'dataOffset');
	            if (dataOffset === undefined) {
	                return undefined;
	            }
	            if (dataOffset + 16 > bytes.length) {
	                return undefined;
	            }
	            const startX = numberField(fields, 'startX');
	            const startY = numberField(fields, 'startY');
	            const hotptX = numberField(fields, 'hotptX');
	            const hotptY = numberField(fields, 'hotptY');
	            if (startX === undefined || startY === undefined || hotptX === undefined || hotptY === undefined) {
	                return undefined;
	            }
	            if (!arePlausibleCoordinates([startX, startY, hotptX, hotptY])) {
	                return undefined;
	            }
	            const displayFlags = numberField(fields, 'displayFlags');
	            const pinType = numberField(fields, 'pinType');
	            return {
	                kind: 'symbol-pin',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                name: nameValue,
	                startX,
	                startY,
	                hotptX,
	                hotptY,
	                ...(displayFlags === undefined ? {} : { displayFlags }),
	                ...(pinType === undefined ? {} : { pinType }),
	            };
	        },
	    };
	    const symbolDisplayPropertyRecordSpec = {
	        name: 'symbol.display-property',
	        family: 'symbol',
	        kind: 'display-property',
	        match: [(ctx) => ctx.bytes.length >= 24 && ctx.bytes.length <= 192],
	        fields: {
	            propertyCode: { kind: 'u32le', offset: 0 },
	            locX: { kind: 'i16le', offset: 4 },
	            locY: { kind: 'i16le', offset: 6 },
	            textStyleId: { kind: 'u16le', offset: 8 },
	            color: { kind: 'u8', offset: 10 },
	            displayType: { kind: 'u16le', offset: 12 },
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 32, maxLength: 512 }),
	            },
	            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 32 },
	        },
	        confidence: 'heuristic',
	        derive: function materializeSymbolDisplayPropertyObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const bytes = record.payload;
	            if (bytes.length < 24 || bytes.length > 192) {
	                return undefined;
	            }
	            const propertyCode = numberField(fields, 'propertyCode');
	            const locX = numberField(fields, 'locX');
	            const locY = numberField(fields, 'locY');
	            const textStyleId = numberField(fields, 'textStyleId');
	            const color = numberField(fields, 'color');
	            const displayType = numberField(fields, 'displayType');
	            if (propertyCode === undefined ||
	                locX === undefined ||
	                locY === undefined ||
	                textStyleId === undefined ||
	                color === undefined ||
	                displayType === undefined) {
	                return undefined;
	            }
	            if (propertyCode < 1 ||
	                propertyCode > 10_000 ||
	                textStyleId > 4096 ||
	                color > 255 ||
	                displayType > 16 ||
	                !arePlausibleCoordinates([locX, locY])) {
	                return undefined;
	            }
	            const strings = stringArrayField(fields, 'countedStrings');
	            if (!hasBytes(bytes, 14, [0x27, 0x22]) && strings.length === 0) {
	                return undefined;
	            }
	            return {
	                kind: 'display-property',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                propertyCode,
	                locX,
	                locY,
	                textStyleId,
	                color,
	                displayType,
	                strings,
	            };
	        },
	    };
	    const symbolPartValueRecordSpec = {
	        name: 'symbol.part-value',
	        family: 'symbol',
	        kind: 'part-value',
	        match: [(ctx) => ctx.bytes.length >= 24],
	        fields: {
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 16, maxLength: 512 }),
	            },
	            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 16 },
	        },
	        derive: function materializePartValueObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const strings = stringArrayField(fields, 'countedStrings');
	            const referencePrefix = strings.find((string) => string.offset === 20);
	            const value = strings.find((string) => string.offset === 24);
	            if (!referencePrefix && !value) {
	                return undefined;
	            }
	            return {
	                kind: 'part-value',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                ...(referencePrefix ? { referencePrefix: referencePrefix.value } : {}),
	                ...(value ? { value: value.value } : {}),
	                strings,
	            };
	        },
	    };
	    const symbolNamedRecordRecordSpec = {
	        name: 'symbol.named-record',
	        family: 'symbol',
	        kind: 'named-record',
	        match: [(ctx) => ctx.bytes.length >= 4],
	        fields: {
	            name: { kind: 'counted-ascii', offset: 0, maxLength: 512, optional: true },
	            countedStrings: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 64, maxLength: 512 }),
	            },
	            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 64 },
	        },
	        confidence: 'heuristic',
	        derive: function materializeNamedRecordObject(fields, ctx) {
	            const record = terminatedPayloadFromContext(ctx);
	            const strings = stringArrayField(fields, 'countedStrings');
	            const first = strings[0];
	            if (!first || first.offset !== 0 || isStreamViewName(first.value, ctx.streamName)) {
	                return undefined;
	            }
	            return {
	                kind: 'named-record',
	                offset: record.offset,
	                byteLength: record.byteLength,
	                payloadByteLength: record.payloadByteLength,
	                name: first.value,
	                strings,
	            };
	        },
	    };
	    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS = [
	        symbolPinNumberMapInlineRecordSpec,
	        symbolViewHeaderRecordSpec,
	        symbolPinRecordSpec,
	        symbolDisplayPropertyRecordSpec,
	        symbolPartValueRecordSpec,
	        symbolNamedRecordRecordSpec,
	    ];
	    const pageDrawnInstScanRecordSpec = {
	        name: 'page.drawn-inst-scan',
	        family: 'page',
	        kind: 'drawn-inst',
	        match: [(ctx) => ctx.offset + 24 <= ctx.bytes.length],
	        fields: {
	            recordedNameIndex: { kind: 'u32le', offset: -11, optional: true, when: (ctx) => ctx.offset >= 11 },
	            dbId: { kind: 'u32le', offset: 0 },
	            displayY1: { kind: 'i16le', offset: 4 },
	            displayX1: { kind: 'i16le', offset: 6 },
	            displayY2: { kind: 'i16le', offset: 8 },
	            displayX2: { kind: 'i16le', offset: 10 },
	            locX: { kind: 'i16le', offset: 12 },
	            locY: { kind: 'i16le', offset: 14 },
	            color: { kind: 'u8', offset: 16 },
	            flags: { kind: 'u8', offset: 17 },
	            recordType: { kind: 'u16le', offset: 18 },
	        },
	        confidence: 'heuristic',
	        derive: function materializeDrawnInstPageObject(fields, ctx) {
	            const bytes = ctx.bytes;
	            const offset = ctx.offset;
	            const recordOffset = ctx.payloadOffset ?? offset;
	            if (offset + 24 > bytes.length ||
	                offset < 7 ||
	                !Array.from(bytes.subarray(offset - 7, offset)).every((byte) => byte === 0)) {
	                return undefined;
	            }
	            const dbId = numberField(fields, 'dbId');
	            const displayY1 = numberField(fields, 'displayY1');
	            const displayX1 = numberField(fields, 'displayX1');
	            const displayY2 = numberField(fields, 'displayY2');
	            const displayX2 = numberField(fields, 'displayX2');
	            const locX = numberField(fields, 'locX');
	            const locY = numberField(fields, 'locY');
	            const color = numberField(fields, 'color');
	            const flags = numberField(fields, 'flags');
	            const recordType = numberField(fields, 'recordType');
	            const recordedNameIndex = numberField(fields, 'recordedNameIndex');
	            if (dbId === undefined ||
	                displayY1 === undefined ||
	                displayX1 === undefined ||
	                displayY2 === undefined ||
	                displayX2 === undefined ||
	                locX === undefined ||
	                locY === undefined ||
	                color === undefined ||
	                flags === undefined ||
	                recordType === undefined) {
	                return undefined;
	            }
	            const mirror = flags & 0x01;
	            const rotation = flags & 0x06;
	            if (dbId < 1 ||
	                dbId > 1_000_000_000 ||
	                color !== 48 ||
	                flags > 16 ||
	                displayX2 < displayX1 ||
	                displayY2 < displayY1 ||
	                !arePlausibleCoordinates([displayX1, displayY1, displayX2, displayY2, locX, locY])) {
	                return undefined;
	            }
	            if (displayX1 - locX < -100 ||
	                displayX1 - locX > 0 ||
	                displayY1 - locY < -100 ||
	                displayY1 - locY > 0 ||
	                displayX2 <= locX ||
	                displayY2 <= locY ||
	                displayX2 - locX > 5000 ||
	                displayY2 - locY > 5000) {
	                return undefined;
	            }
	            const hasDisplayTrailer = offset + 24 <= bytes.length &&
	                (hasBytes(bytes, offset + 22, [0x27, 0x22]) || hasBytes(bytes, offset + 22, [0x18, 0x18]));
	            if (!hasDisplayTrailer && bytes.length - offset < 40) {
	                return undefined;
	            }
	            const recordedName = recordedNameIndex !== undefined ? libraryCatalogValue(ctx, recordedNameIndex) : undefined;
	            return {
	                kind: 'drawn-inst',
	                offset: recordOffset,
	                byteLength: Math.min((ctx.byteLength ?? bytes.length) - offset, 54),
	                payloadByteLength: Math.min((ctx.payloadByteLength ?? bytes.length) - offset, 54),
	                dbId,
	                locX,
	                locY,
	                color,
	                mirror,
	                rotation,
	                displayX1,
	                displayY1,
	                displayX2,
	                displayY2,
	                relativeDisplayX1: displayX1 - locX,
	                relativeDisplayY1: displayY1 - locY,
	                relativeDisplayX2: displayX2 - locX,
	                relativeDisplayY2: displayY2 - locY,
	                flags,
	                recordType,
	                ...(recordedNameIndex !== undefined ? { recordedNameIndex } : {}),
	                ...(recordedName !== undefined ? { recordedName } : {}),
	            };
	        },
	    };
	    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_SCAN_RECORD_SPECS = [pageDrawnInstScanRecordSpec];
	    const symbolPinNumberMapTailRecordSpec = {
	        name: 'symbol.pin-number-map-tail',
	        family: 'symbol',
	        kind: 'pin-number-map',
	        match: [(ctx) => ctx.offset + 10 <= ctx.bytes.length],
	        fields: {
	            pinNumberMap: {
	                kind: 'custom',
	                offset: 0,
	                read: (ctx) => tailPinNumberMapFromContext(ctx),
	            },
	            strings: {
	                kind: 'ascii-runs',
	                offset: 0,
	                minLength: 1,
	                maxCount: 128,
	                when: (ctx) => ctx.fields?.pinNumberMap !== undefined,
	                byteLength: (ctx) => {
	                    const map = ctx.fields?.pinNumberMap;
	                    return map === undefined ? undefined : map.offset - ctx.offset + map.byteLength;
	                },
	            },
	        },
	        derive: function materializeTailPinNumberMapObject(fields, _ctx) {
	            const map = fields.pinNumberMap;
	            if (!map) {
	                return undefined;
	            }
	            return {
	                kind: 'pin-number-map',
	                ...map,
	            };
	        },
	    };
	    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_TAIL_RECORD_SPECS = [symbolPinNumberMapTailRecordSpec];
	})(OrcadDsnCfbDeclarativeSpecs$1 || (OrcadDsnCfbDeclarativeSpecs.OrcadDsnCfbDeclarativeSpecs = OrcadDsnCfbDeclarativeSpecs$1 = {}));
	
	return OrcadDsnCfbDeclarativeSpecs;
}

var OrcadDsnCfbLogicalSchemaConverter = {};

var LogicalSchema = {};

var hasRequiredLogicalSchema;

function requireLogicalSchema () {
	if (hasRequiredLogicalSchema) return LogicalSchema;
	hasRequiredLogicalSchema = 1;
	Object.defineProperty(LogicalSchema, "__esModule", { value: true });
	LogicalSchema.OrCadDSN = void 0;
	var OrCadDSN;
	(function (OrCadDSN) {
	    (function (SymbolNameEnum) {
	        SymbolNameEnum["Offpage"] = "OFFPAGE";
	        SymbolNameEnum["OffpageBothIn"] = "OFFPAGE_BOTH<>-IN";
	        SymbolNameEnum["OffpageleftL"] = "OFFPAGELEFT-L";
	        SymbolNameEnum["OffpagerightL"] = "OFFPAGERIGHT-L";
	        SymbolNameEnum["OffpageleftB"] = "OFFPAGELEFT-B";
	        SymbolNameEnum["OffpagerightB"] = "OFFPAGERIGHT-B";
	        SymbolNameEnum["OffpageleftR"] = "OFFPAGELEFT-R";
	        SymbolNameEnum["OffpagerightR"] = "OFFPAGERIGHT-R";
	        SymbolNameEnum["OffpageInR"] = "OFFPAGE-IN-R";
	        SymbolNameEnum["OffpageInL"] = "OFFPAGE-IN-L";
	        SymbolNameEnum["OffpageOutR"] = "OFFPAGE-OUT-R";
	        SymbolNameEnum["OffpageOutL"] = "OFFPAGE-OUT-L";
	        SymbolNameEnum["PortbothL"] = "PORTBOTH-L";
	        SymbolNameEnum["PortbothR"] = "PORTBOTH-R";
	        SymbolNameEnum["PortleftL"] = "PORTLEFT-L";
	        SymbolNameEnum["PortleftR"] = "PORTLEFT-R";
	        SymbolNameEnum["PortnoR"] = "PORTNO-R";
	        SymbolNameEnum["PortrightL"] = "PORTRIGHT-L";
	        SymbolNameEnum["PortrightR"] = "PORTRIGHT-R";
	        SymbolNameEnum["Off_L_bi"] = "OFF_L_BI";
	    })(OrCadDSN.SymbolNameEnum || (OrCadDSN.SymbolNameEnum = {}));
	    OrCadDSN.OCCURRENCE_NET_SYSTEM_ID_PROP_PREFIX = '@occurrence-net-system-id:';
	})(OrCadDSN || (LogicalSchema.OrCadDSN = OrCadDSN = {}));
	
	return LogicalSchema;
}

var OrcadDsnCfbParser = {};

var OrcadDsnCfbCfb = {};

var hasRequiredOrcadDsnCfbCfb;

function requireOrcadDsnCfbCfb () {
	if (hasRequiredOrcadDsnCfbCfb) return OrcadDsnCfbCfb;
	hasRequiredOrcadDsnCfbCfb = 1;
	Object.defineProperty(OrcadDsnCfbCfb, "__esModule", { value: true });
	OrcadDsnCfbCfb.readOrCadDsnCfbEntries = readOrCadDsnCfbEntries;
	const cfb_interop_1 = requireCfbInterop();
	function toUint8Array(content) {
	    if (!content) {
	        return undefined;
	    }
	    if (content instanceof Uint8Array) {
	        return content;
	    }
	    if (content instanceof ArrayBuffer) {
	        return new Uint8Array(content);
	    }
	    if (Array.isArray(content)) {
	        return Uint8Array.from(content);
	    }
	    return undefined;
	}
	function baseName(path) {
	    const withoutTrailing = path.endsWith('/') ? path.slice(0, -1) : path;
	    const slash = withoutTrailing.lastIndexOf('/');
	    return slash === -1 ? withoutTrailing : withoutTrailing.slice(slash + 1);
	}
	function storagePath(path) {
	    const withoutTrailing = path.endsWith('/') ? path.slice(0, -1) : path;
	    const slash = withoutTrailing.lastIndexOf('/');
	    return slash === -1 ? '' : withoutTrailing.slice(0, slash);
	}
	function readOrCadDsnCfbEntries(fileContents) {
	    const container = cfb_interop_1.CFB.read(fileContents, { type: 'array' });
	    const fileIndex = container.FileIndex;
	    return fileIndex.map((entry, index) => {
	        const path = container.FullPaths[index] ?? '';
	        const content = toUint8Array(entry.content);
	        return {
	            path,
	            name: baseName(path),
	            type: entry.type ?? 0,
	            size: entry.size ?? content?.byteLength ?? 0,
	            storagePath: storagePath(path),
	            ...(content ? { content } : {}),
	        };
	    });
	}
	
	return OrcadDsnCfbCfb;
}

var OrcadDsnCfbMaterializer = {};

var OrcadDsnCfbOccurrenceOwnership = {};

var hasRequiredOrcadDsnCfbOccurrenceOwnership;

function requireOrcadDsnCfbOccurrenceOwnership () {
	if (hasRequiredOrcadDsnCfbOccurrenceOwnership) return OrcadDsnCfbOccurrenceOwnership;
	hasRequiredOrcadDsnCfbOccurrenceOwnership = 1;
	Object.defineProperty(OrcadDsnCfbOccurrenceOwnership, "__esModule", { value: true });
	OrcadDsnCfbOccurrenceOwnership.HIERARCHY_ROW_ID_THRESHOLD = OrcadDsnCfbOccurrenceOwnership.HierarchyAdoptionValue = void 0;
	OrcadDsnCfbOccurrenceOwnership.hierarchyReferenceCandidate = hierarchyReferenceCandidate;
	OrcadDsnCfbOccurrenceOwnership.hierarchyPlacementKey = hierarchyPlacementKey;
	OrcadDsnCfbOccurrenceOwnership.hierarchyAdoptionVeto = hierarchyAdoptionVeto;
	OrcadDsnCfbOccurrenceOwnership.readHierarchyOccurrenceTrust = readHierarchyOccurrenceTrust;
	const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
	var HierarchyAdoptionValue;
	(function (HierarchyAdoptionValue) {
	    HierarchyAdoptionValue["Reference"] = "reference";
	    HierarchyAdoptionValue["Section"] = "section";
	})(HierarchyAdoptionValue || (OrcadDsnCfbOccurrenceOwnership.HierarchyAdoptionValue = HierarchyAdoptionValue = {}));
	OrcadDsnCfbOccurrenceOwnership.HIERARCHY_ROW_ID_THRESHOLD = 100_000;
	function hierarchyReferenceCandidate(entry) {
	    const viewName = entry.owningViewName ?? entry.parentViewName;
	    const refInstId = entry.refInstId ?? entry.dbId;
	    const reference = entry.name;
	    const occurrenceId = entry.occurrenceId ?? (entry.parentViewName === undefined ? undefined : entry.recordId);
	    if (entry.parentBranchOffset !== undefined ||
	        viewName === undefined ||
	        refInstId === undefined ||
	        refInstId <= 0 ||
	        entry.referenceStringOffset === undefined ||
	        reference === undefined ||
	        reference.length > 64 ||
	        !(/^[A-Za-z][A-Za-z0-9_.-]*\d[A-Za-z0-9_.-]*$/.test(reference) ||
	            /^[A-Za-z]+\?$/.test(reference) ||
	            /^\d+$/.test(reference))) {
	        return undefined;
	    }
	    return {
	        refInstId,
	        reference,
	        viewName,
	        offset: entry.referenceStringOffset,
	        occurrenceId,
	        branchOffset: entry.ownershipBranchOffset ?? entry.parentBranchOffset,
	    };
	}
	function hierarchyPlacementKey(view, id) {
	    return JSON.stringify([view.toUpperCase(), id]);
	}
	function hierarchyAdoptionVeto(trust, stream, view, id, branchOffset, occurrenceId, value = HierarchyAdoptionValue.Reference) {
	    if (trust.streamVetoes.has(stream)) {
	        return 'stream scope is unknowable';
	    }
	    if (view !== undefined && trust.viewVetoes.has(view.toUpperCase())) {
	        return 'view has an untrusted branch';
	    }
	    if (id !== undefined && trust.tombstones.get(stream)?.has(id)) {
	        return 'identity was claimed by an untrusted branch';
	    }
	    if (branchOffset !== undefined &&
	        id !== undefined &&
	        (occurrenceId === undefined ?
	            trust.branchReferenceConflicts.has(JSON.stringify([stream, branchOffset, id]))
	            : trust.conflicts.get(stream)?.has(JSON.stringify([branchOffset, occurrenceId, id])))) {
	        return 'hierarchy-reference-conflict within one occurrence path';
	    }
	    if (branchOffset === undefined &&
	        view !== undefined &&
	        id !== undefined &&
	        value === HierarchyAdoptionValue.Section &&
	        trust.sectionConflicts.has(hierarchyPlacementKey(view, id))) {
	        return 'hierarchy-section-conflict for decoded view/identity';
	    }
	    return undefined;
	}
	function readHierarchyOccurrenceTrust(document) {
	    const trustedEntries = new Set();
	    const rejectedEntries = [];
	    const tombstones = new Map();
	    const viewVetoes = new Set();
	    const streamVetoes = new Set();
	    const conflicts = new Map();
	    const branchReferenceConflicts = new Set();
	    const sectionConflicts = new Set();
	    const sectionValues = new Map();
	    const branchReferenceValues = new Map();
	    const viewsByStream = new Map();
	    const library = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
	    const strings = library?.kind === 'library' ? library.stringCatalog : undefined;
	    for (const stream of document.streams) {
	        if (stream.parsed?.kind !== 'hierarchy') {
	            continue;
	        }
	        const branches = new Map();
	        const identities = new Set();
	        const ambiguous = new Set();
	        const associatedViews = new Set([stream.parsed.viewName.toUpperCase()]);
	        const pathCounts = new Map();
	        for (const entry of stream.parsed.entries) {
	            const view = entry.owningViewName ?? entry.parentViewName;
	            const id = entry.refInstId ?? entry.dbId;
	            if (view === undefined || id === undefined || entry.referenceStringOffset === undefined) {
	                continue;
	            }
	            const key = hierarchyPlacementKey(view, id);
	            const index = entry.sectionSelectorStringIndex;
	            if (index !== undefined) {
	                const selector = strings?.[index] ?? index;
	                const sections = sectionValues.get(key) ?? new Set();
	                if (selector !== '') {
	                    sections.add(selector);
	                }
	                sectionValues.set(key, sections);
	                if (sections.size > 1) {
	                    sectionConflicts.add(key);
	                }
	            }
	            const branch = entry.ownershipBranchOffset ?? entry.parentBranchOffset;
	            if (branch !== undefined) {
	                const occurrenceId = entry.occurrenceId ?? entry.recordId;
	                const path = JSON.stringify([branch, occurrenceId, id]);
	                const pathCount = (pathCounts.get(path) ?? 0) + 1;
	                pathCounts.set(path, pathCount);
	                const branchKey = JSON.stringify([stream.path, branch, id]);
	                const branchValues = branchReferenceValues.get(branchKey) ?? new Set();
	                branchValues.add(entry.name);
	                branchReferenceValues.set(branchKey, branchValues);
	                if (branchValues.size > 1) {
	                    branchReferenceConflicts.add(branchKey);
	                }
	                if (pathCount > 1) {
	                    ambiguous.add(path);
	                    branchReferenceConflicts.add(branchKey);
	                    sectionConflicts.add(key);
	                }
	            }
	        }
	        tombstones.set(stream.path, identities);
	        conflicts.set(stream.path, ambiguous);
	        viewsByStream.set(stream.path, associatedViews);
	        for (const branch of stream.parsed.ownershipBranches ?? []) {
	            const existing = branches.get(branch.offset);
	            if (existing !== undefined) {
	                existing.trusted = false;
	            }
	            else {
	                branches.set(branch.offset, {
	                    trusted: branch.state === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Resolved && branch.viewName !== undefined,
	                    viewName: branch.viewName,
	                    claims: new Map(),
	                });
	            }
	            if (branch.viewName !== undefined) {
	                associatedViews.add(branch.viewName.toUpperCase());
	            }
	        }
	        const rows = stream.parsed.entries.filter((entry) => entry.ownershipState !== undefined || entry.ownershipBranchOffset !== undefined);
	        for (const entry of rows) {
	            const branch = entry.ownershipBranchOffset === undefined ? undefined : branches.get(entry.ownershipBranchOffset);
	            if (branch !== undefined &&
	                (entry.ownershipState !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved ||
	                    entry.owningViewName === undefined ||
	                    entry.sectionSelectorStringIndex === undefined)) {
	                branch.trusted = false;
	            }
	            if (entry.owningViewName !== undefined) {
	                associatedViews.add(entry.owningViewName.toUpperCase());
	            }
	        }
	        for (const branch of branches.values()) {
	            if (!branch.trusted) {
	                if (branch.viewName === undefined) {
	                    streamVetoes.add(stream.path);
	                }
	                else {
	                    viewVetoes.add(branch.viewName.toUpperCase());
	                }
	            }
	        }
	        for (const entry of rows) {
	            const branch = entry.ownershipBranchOffset === undefined ? undefined : branches.get(entry.ownershipBranchOffset);
	            const id = entry.refInstId ?? entry.dbId;
	            if (branch?.trusted === true) {
	                trustedEntries.add(entry);
	                if (id !== undefined) {
	                    const path = JSON.stringify([entry.occurrenceId, id]);
	                    const values = branch.claims.get(path) ?? {
	                        id,
	                        occurrenceId: entry.occurrenceId,
	                        count: 0,
	                        references: new Set(),
	                        selectors: new Set(),
	                    };
	                    values.count++;
	                    if (entry.name !== undefined && entry.name !== '') {
	                        values.references.add(entry.name);
	                    }
	                    const index = entry.sectionSelectorStringIndex;
	                    if (index !== undefined) {
	                        const selector = strings?.[index] ?? index;
	                        if (selector !== '') {
	                            values.selectors.add(selector);
	                        }
	                    }
	                    branch.claims.set(path, values);
	                }
	            }
	            else {
	                rejectedEntries.push({ streamPath: stream.path, entry });
	                if (id !== undefined) {
	                    identities.add(id);
	                }
	                if (branch === undefined) {
	                    streamVetoes.add(stream.path);
	                }
	            }
	        }
	        for (const [offset, branch] of branches) {
	            if (branch.viewName === undefined) {
	                continue;
	            }
	            for (const values of branch.claims.values()) {
	                if (values.count > 1 || values.references.size > 1 || values.selectors.size > 1) {
	                    ambiguous.add(JSON.stringify([offset, values.occurrenceId, values.id]));
	                    branchReferenceConflicts.add(JSON.stringify([stream.path, offset, values.id]));
	                    sectionConflicts.add(hierarchyPlacementKey(branch.viewName, values.id));
	                }
	            }
	        }
	    }
	    return {
	        trustedEntries,
	        tombstones,
	        viewVetoes,
	        streamVetoes,
	        conflicts,
	        branchReferenceConflicts,
	        sectionConflicts,
	        viewsByStream,
	        rejectedEntries,
	    };
	}
	
	return OrcadDsnCfbOccurrenceOwnership;
}

var hasRequiredOrcadDsnCfbMaterializer;

function requireOrcadDsnCfbMaterializer () {
	if (hasRequiredOrcadDsnCfbMaterializer) return OrcadDsnCfbMaterializer;
	hasRequiredOrcadDsnCfbMaterializer = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.__testing = exports.PENDING_CONTENTS_VIEW_NAME = exports.ContentsViewNameSentinel = void 0;
		exports.materializeOrCadDsnCfbDocument = materializeOrCadDsnCfbDocument;
		const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
		const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
		const OrcadDsnCfbOccurrenceOwnership_1 = requireOrcadDsnCfbOccurrenceOwnership();
		var ContentsViewNameSentinel;
		(function (ContentsViewNameSentinel) {
		    ContentsViewNameSentinel["pending"] = "\0pending";
		})(ContentsViewNameSentinel || (exports.ContentsViewNameSentinel = ContentsViewNameSentinel = {}));
		exports.PENDING_CONTENTS_VIEW_NAME = ContentsViewNameSentinel.pending;
		function placedSymbolLookupNames(symbolName) {
		    const names = [symbolName];
		    const viewSuffixMatch = /^(.*)\.(?:Normal|IEEE)$/.exec(symbolName);
		    if (viewSuffixMatch) {
		        names.push(viewSuffixMatch[1]);
		    }
		    return names;
		}
		function addSymbolPinLookupEntry(lookup, name, pins) {
		    if (!name || pins.length === 0) {
		        return;
		    }
		    lookup.set(name, pins);
		    const viewSuffixMatch = /^(.*)\.(?:Normal|IEEE)$/.exec(name);
		    if (viewSuffixMatch && !lookup.has(viewSuffixMatch[1])) {
		        lookup.set(viewSuffixMatch[1], pins);
		    }
		}
		function buildSymbolPinLookup(streams) {
		    const lookup = new Map();
		    for (const stream of streams) {
		        const records = stream.decodedSymbolObjects ?? [];
		        if (!records.some((record) => record.kind === 'symbol-pin')) {
		            continue;
		        }
		        const parsed = stream.parsed;
		        const fallbackName = parsed?.kind === 'package' ? parsed.packageName
		            : parsed?.kind === 'symbol' ? parsed.symbolName
		                : undefined;
		        let currentGroupName = fallbackName;
		        let currentPins = [];
		        let sawViewHeader = false;
		        const flushCurrentGroup = () => {
		            addSymbolPinLookupEntry(lookup, currentGroupName, currentPins);
		            currentPins = [];
		        };
		        for (const record of records) {
		            if (record.kind === 'view-header') {
		                flushCurrentGroup();
		                sawViewHeader = true;
		                currentGroupName = record.name;
		                continue;
		            }
		            if (record.kind === 'symbol-pin') {
		                currentPins.push(record);
		            }
		        }
		        flushCurrentGroup();
		        if (!sawViewHeader && fallbackName) {
		            addSymbolPinLookupEntry(lookup, fallbackName, currentPins);
		        }
		    }
		    return lookup;
		}
		function findPlacedSymbolPins(placedSymbol, symbolPinLookup) {
		    for (const name of placedSymbolLookupNames(placedSymbol.symbolName)) {
		        const pins = symbolPinLookup.get(name);
		        if (pins) {
		            return pins;
		        }
		    }
		    return undefined;
		}
		function applySymbolPinMetadata(portInstance, symbolPin) {
		    const nameCorroborated = portInstance.pinName === undefined || portInstance.pinName === symbolPin.name;
		    portInstance.pinNameSource ??=
		        portInstance.pinName === undefined ?
		            OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.SymbolPin
		            : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.Record;
		    portInstance.pinName ??= symbolPin.name;
		    portInstance.startX = symbolPin.startX;
		    portInstance.startY = symbolPin.startY;
		    portInstance.hotptX = symbolPin.hotptX;
		    portInstance.hotptY = symbolPin.hotptY;
		    if (nameCorroborated) {
		        portInstance.type ??= symbolPin.pinType;
		    }
		    return nameCorroborated;
		}
		function materializedPortFromExplicitPort(portInstance) {
		    const nameSource = portInstance.pinNameSource ??
		        (portInstance.pinName !== undefined ? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.Record : undefined);
		    return {
		        name: portInstance.pinName ?? String(portInstance.pinIndex),
		        ...(nameSource !== undefined ? { nameSource } : {}),
		        position: portInstance.position,
		        pinIndex: portInstance.pinIndex,
		        ...(portInstance.startX !== undefined ? { startX: portInstance.startX } : {}),
		        ...(portInstance.startY !== undefined ? { startY: portInstance.startY } : {}),
		        ...(portInstance.hotptX !== undefined ? { hotptX: portInstance.hotptX } : {}),
		        ...(portInstance.hotptY !== undefined ? { hotptY: portInstance.hotptY } : {}),
		        x: portInstance.x,
		        y: portInstance.y,
		        ...(portInstance.wireDbId !== undefined ? { wireDbId: portInstance.wireDbId } : {}),
		        isNoConnect: portInstance.isNoConnect,
		        isBus: portInstance.isBus ?? portInstance.pinName?.includes('[') ?? false,
		        ...(portInstance.type !== undefined ? { type: portInstance.type } : {}),
		        ...(portInstance.visible !== undefined ? { visible: portInstance.visible } : {}),
		        ...(portInstance.isPlacedInstPin !== undefined ? { isPlacedInstPin: portInstance.isPlacedInstPin } : {}),
		        source: 'record',
		        isExplicit: true,
		    };
		}
		function materializedPortFromSymbolPin(symbolPin, position) {
		    return {
		        name: symbolPin.name,
		        nameSource: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.SymbolPin,
		        position,
		        pinIndex: position + 1,
		        startX: symbolPin.startX,
		        startY: symbolPin.startY,
		        hotptX: symbolPin.hotptX,
		        hotptY: symbolPin.hotptY,
		        isNoConnect: false,
		        isBus: symbolPin.name.includes('['),
		        ...(symbolPin.pinType !== undefined ? { type: symbolPin.pinType } : {}),
		        source: 'symbol-pin',
		        isExplicit: false,
		    };
		}
		function materializedPortFromDrawnPort(drawnPort) {
		    return {
		        name: drawnPort.name,
		        startX: drawnPort.startX,
		        startY: drawnPort.startY,
		        hotptX: drawnPort.hotptX,
		        hotptY: drawnPort.hotptY,
		        isNoConnect: false,
		        isBus: drawnPort.isBus,
		        ...(drawnPort.type !== undefined ? { type: drawnPort.type } : {}),
		        ...(drawnPort.visible !== undefined ? { visible: drawnPort.visible } : {}),
		        source: 'drawn-port',
		        isExplicit: true,
		    };
		}
		function pkgNameFromGraphicName(graphicName) {
		    return graphicName.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
		}
		function baseDeviceDesignator(value) {
		    return value.replace(/\d+$/u, '').trim() || value;
		}
		function isSelfReferencePlaceholder(value) {
		    return /^&+$/.test(value.trim());
		}
		function userPropertiesFromDisplayProperties(displayProperties) {
		    const properties = [];
		    for (const property of displayProperties ?? []) {
		        const value = property.displayedValue;
		        if (value === undefined || isSelfReferencePlaceholder(value)) {
		            continue;
		        }
		        properties.push({
		            name: property.propertyName ?? `<unnamed property ${property.propertyCode}>`,
		            value,
		            source: 'display-property',
		        });
		    }
		    return properties;
		}
		function libraryStringCatalogFromStreams(streams) {
		    for (const stream of streams) {
		        if (stream.parsed?.kind === 'library' && stream.parsed.stringCatalog) {
		            return stream.parsed.stringCatalog;
		        }
		    }
		    return undefined;
		}
		function isCatalogPropertyName(value) {
		    return value !== undefined && value.length > 0 && value.length <= 64 && /^[A-Za-z]/.test(value);
		}
		function userPropertiesFromPropertyPairs(propertyPairs, stringCatalog) {
		    const properties = [];
		    for (const pair of propertyPairs) {
		        const name = stringCatalog[pair.nameId];
		        const value = stringCatalog[pair.valueId];
		        if (!isCatalogPropertyName(name) || value === undefined || isSelfReferencePlaceholder(value)) {
		            continue;
		        }
		        properties.push({ name, value, source: 'library-catalog' });
		    }
		    return properties;
		}
		function displayCatalogValue(placedSymbol, stringCatalog, diagnostics, streamPath) {
		    const indices = [
		        placedSymbol.referenceTail?.valueStringIndex,
		        ...(placedSymbol.displayProperties ?? [])
		            .filter((property) => {
		            const pointerValue = property.libraryStringIndex === undefined ? undefined : stringCatalog[property.libraryStringIndex];
		            return stringCatalog[property.propertyCode] === 'Value' || pointerValue !== property.displayedValue;
		        })
		            .map((property) => property.libraryStringIndex),
		    ].filter((index) => index !== undefined && index > 0);
		    const unresolved = indices.filter((index) => stringCatalog[index] === undefined);
		    const values = new Set(indices.map((index) => stringCatalog[index]).filter((value) => value !== undefined));
		    if (unresolved.length > 0 || values.size > 1) {
		        placedSymbol.valueState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld;
		        diagnostics.push({
		            severity: 'warning',
		            code: 'placement-value-withheld',
		            streamPath,
		            offset: placedSymbol.offset,
		            message: `CFB placement value: tail/display catalogue pointers ${JSON.stringify(indices)} do not resolve and agree; unresolved=${JSON.stringify(unresolved)}, values=${JSON.stringify([...values])}`,
		        });
		        return undefined;
		    }
		    return placedSymbol.valueState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld ? undefined : [...values][0];
		}
		function enrichPageObjectsFromStringCatalog(streams, stringCatalog, diagnostics) {
		    const referencedNames = new Set();
		    const resolveDisplayProperty = (property) => {
		        const name = stringCatalog[property.propertyCode];
		        if (isCatalogPropertyName(name)) {
		            property.propertyName = name;
		            referencedNames.add(name);
		        }
		    };
		    for (const stream of streams) {
		        for (const pageObject of stream.decodedPageObjects ?? []) {
		            if (pageObject.kind === 'wire' && pageObject.propertyPairs) {
		                pageObject.userProperties = userPropertiesFromPropertyPairs(pageObject.propertyPairs, stringCatalog);
		            }
		            if (pageObject.kind === 'display-property') {
		                resolveDisplayProperty(pageObject);
		            }
		            if (pageObject.kind === 'drawn-inst') {
		                (pageObject.displayProperties ?? []).forEach(resolveDisplayProperty);
		            }
		            if (pageObject.kind === 'placed-symbol') {
		                const placedName = stringCatalog[pageObject.recordType];
		                if (placedName) {
		                    pageObject.name = placedName;
		                }
		                (pageObject.displayProperties ?? []).forEach(resolveDisplayProperty);
		                for (const instanceProperty of pageObject.instanceProperties ?? []) {
		                    const name = stringCatalog[instanceProperty.propertyCode];
		                    if (isCatalogPropertyName(name)) {
		                        instanceProperty.name = name;
		                        referencedNames.add(name);
		                    }
		                }
		                const exactValue = displayCatalogValue(pageObject, stringCatalog, diagnostics, stream.path);
		                if (pageObject.propertyPairs) {
		                    const properties = userPropertiesFromPropertyPairs(pageObject.propertyPairs, stringCatalog);
		                    pageObject.resolvedPropertyPairCount = properties.length;
		                    properties.forEach((property) => referencedNames.add(property.name));
		                    if (exactValue !== undefined && !properties.some((property) => property.name === 'Value')) {
		                        properties.push({ name: 'Value', value: exactValue, source: 'library-catalog' });
		                    }
		                    if (properties.length > 0) {
		                        pageObject.userProperties = properties;
		                    }
		                }
		            }
		        }
		    }
		    return referencedNames;
		}
		function applyLibraryPropertyNames(streams, referencedNames) {
		    const names = Array.from(referencedNames).sort();
		    for (const stream of streams) {
		        if (stream.parsed?.kind === 'library') {
		            stream.parsed.propertyNames = names;
		        }
		    }
		}
		function buildHierarchyLookup(streams, diagnostics = [], occurrenceTrust = (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)({ streams })) {
		    const lookup = new Map();
		    const candidates = new Map();
		    for (const stream of streams) {
		        const parsed = stream.parsed;
		        if (parsed?.kind !== 'hierarchy') {
		            continue;
		        }
		        for (const entry of parsed.entries) {
		            if (entry.dbId !== undefined && entry.contentsViewName !== undefined) {
		                const parentView = entry.parentViewName;
		                const reason = parentView === undefined ? 'counted parent is unknown' : (((0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyAdoptionVeto)(occurrenceTrust, stream.path, entry.contentsViewName, entry.dbId) ??
		                    (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyAdoptionVeto)(occurrenceTrust, stream.path, parentView, entry.dbId, entry.parentBranchOffset, entry.recordId)));
		                if (reason !== undefined || parentView === undefined) {
		                    diagnostics.push({
		                        severity: 'warning',
		                        code: 'hierarchy-adoption-withheld',
		                        streamPath: stream.path,
		                        offset: entry.offset,
		                        message: `CFB hierarchy [hierarchy-adoption-withheld] drawn-instance dbId=${entry.dbId}, parentOffset=${entry.parentBranchOffset}: ${reason}`,
		                    });
		                    continue;
		                }
		                const key = (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyPlacementKey)(parentView, entry.dbId);
		                const claims = candidates.get(key) ?? [];
		                claims.push(entry);
		                candidates.set(key, claims);
		            }
		        }
		    }
		    for (const [key, rows] of candidates) {
		        if (new Set(rows.map((row) => row.contentsViewName)).size === 1) {
		            lookup.set(key, rows[0]);
		        }
		        else {
		            diagnostics.push({
		                severity: 'warning',
		                code: 'hierarchy-adoption-withheld',
		                message: `CFB hierarchy [hierarchy-adoption-withheld] drawn-instance ${key}: conflicting child views`,
		            });
		        }
		    }
		    return lookup;
		}
		exports.__testing = { buildHierarchyLookup };
		function enrichPlacedSymbolMetadata(placedSymbol, diagnostics, streamPath) {
		    const catalogUserProperties = (placedSymbol.userProperties ?? []).filter((property) => property.source === 'library-catalog');
		    const userProperties = catalogUserProperties.length > 0 ?
		        catalogUserProperties
		        : userPropertiesFromDisplayProperties(placedSymbol.displayProperties);
		    if (placedSymbol.valueState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld) {
		        for (const properties of [userProperties, placedSymbol.userProperties ?? []]) {
		            for (let index = properties.length - 1; index >= 0; index--) {
		                if (properties[index].name === 'Value') {
		                    properties.splice(index, 1);
		                }
		            }
		        }
		        placedSymbol.partValue = undefined;
		    }
		    if (userProperties.length > 0) {
		        placedSymbol.userProperties = userProperties;
		    }
		    const displayReferences = (placedSymbol.displayProperties ?? [])
		        .filter((property) => property.propertyName === 'Part Reference')
		        .map((property) => property.displayedValue)
		        .filter((value) => value !== undefined && value.length > 0);
		    if (placedSymbol.referenceTail !== undefined) {
		        const reference = placedSymbol.referenceTail.reference;
		        const candidates = [...displayReferences, placedSymbol.reference].filter((value) => value !== undefined && value.length > 0);
		        if (candidates.some((candidate) => candidate !== reference)) {
		            placedSymbol.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
		            diagnostics.push({
		                severity: 'warning',
		                code: 'placement-reference-tail-withheld',
		                streamPath,
		                offset: placedSymbol.referenceTail.offset,
		                message: `CFB placement reference: tail ${JSON.stringify(reference)} disagrees with decoded reference evidence ${JSON.stringify(candidates)}`,
		            });
		        }
		        else if (placedSymbol.referenceState !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld) {
		            placedSymbol.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Resolved;
		            placedSymbol.reference = reference.length === 0 || reference.endsWith('?') ? undefined : reference;
		        }
		    }
		    else {
		        if (displayReferences.some((reference) => reference !== displayReferences[0])) {
		            placedSymbol.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
		            diagnostics.push({
		                severity: 'warning',
		                code: 'placement-reference-tail-withheld',
		                streamPath,
		                offset: placedSymbol.offset,
		                message: `CFB placement reference: explicit Part Reference displays disagree: ${JSON.stringify(displayReferences)}`,
		            });
		        }
		        else {
		            placedSymbol.reference ??= displayReferences[0];
		        }
		    }
		    if (placedSymbol.referenceState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld) {
		        placedSymbol.reference = undefined;
		    }
		    const pkgName = placedSymbol.role === 'part-inst' ? pkgNameFromGraphicName(placedSymbol.graphicName) : undefined;
		    const pcbLib = userProperties.find((property) => property.name === 'PCB Footprint')?.value;
		    const partValue = userProperties.find((property) => property.name === 'Value' && property.value !== placedSymbol.reference)?.value;
		    if (partValue) {
		        placedSymbol.partValue = partValue;
		    }
		    placedSymbol.resolvedMetadata = {
		        kind: placedSymbol.role,
		        name: placedSymbol.reference ? `INS${placedSymbol.dbId}` : undefined,
		        ...(placedSymbol.reference ? { reference: placedSymbol.reference } : {}),
		        ...(placedSymbol.partValue ? { partValue: placedSymbol.partValue } : {}),
		        ...(pkgName ? { pkgName } : {}),
		        contentsLibName: '',
		        contentsViewName: placedSymbol.role === 'part-inst' ? exports.PENDING_CONTENTS_VIEW_NAME : undefined,
		        contentsViewType: placedSymbol.role === 'part-inst' ? 5 : undefined,
		        isPrimitiveProp: placedSymbol.role === 'part-inst' ? 2 : undefined,
		        powerPinsVisible: false,
		        mirror: (placedSymbol.flags & 0x04) !== 0 ? 1 : 0,
		        rotation: placedSymbol.flags & 0x03,
		        ...(pcbLib ? { pcbLib } : {}),
		        ...(userProperties.length > 0 ? { userProperties } : {}),
		    };
		}
		function enrichDrawnInstMetadata(drawnInst, hierarchyLookup, viewName) {
		    const hierarchy = hierarchyLookup.get((0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyPlacementKey)(viewName, drawnInst.dbId));
		    if (hierarchy?.contentsViewName) {
		        drawnInst.contentsViewName = hierarchy.contentsViewName;
		        drawnInst.partValue = hierarchy.contentsViewName;
		    }
		    drawnInst.contentsLibName = '';
		    drawnInst.contentsViewType = 1;
		    drawnInst.isPrimitiveProp = 2;
		    if (drawnInst.reference && !drawnInst.deviceDesignator) {
		        drawnInst.deviceDesignator = drawnInst.reference;
		    }
		    if (drawnInst.recordFrame !== undefined && drawnInst.recordedName !== undefined) {
		        drawnInst.name = drawnInst.recordedName;
		    }
		    else if (drawnInst.deviceDesignator && !drawnInst.name) {
		        drawnInst.name = baseDeviceDesignator(drawnInst.deviceDesignator);
		    }
		    drawnInst.resolvedMetadata = {
		        kind: 'drawn-inst',
		        ...(drawnInst.name ? { name: drawnInst.name } : {}),
		        ...(drawnInst.reference ? { reference: drawnInst.reference } : {}),
		        ...(drawnInst.partValue ? { partValue: drawnInst.partValue } : {}),
		        ...(drawnInst.deviceDesignator ? { deviceDesignator: drawnInst.deviceDesignator } : {}),
		        contentsLibName: drawnInst.contentsLibName,
		        contentsViewName: drawnInst.contentsViewName,
		        contentsViewType: drawnInst.contentsViewType,
		        isPrimitiveProp: drawnInst.isPrimitiveProp,
		        mirror: drawnInst.mirror,
		        rotation: drawnInst.rotation,
		    };
		    drawnInst.materializedPortInstances = (drawnInst.portInstances ?? []).map(materializedPortFromDrawnPort);
		}
		function enrichPagePortInstancesFromSymbolPins(streams, diagnostics, occurrenceTrust) {
		    const symbolPinLookup = buildSymbolPinLookup(streams);
		    const hierarchyLookup = buildHierarchyLookup(streams, diagnostics, occurrenceTrust);
		    const uncorroboratedSymbolNames = new Map();
		    for (const stream of streams) {
		        if (!stream.decodedPageObjects) {
		            continue;
		        }
		        if (stream.decodedPageObjectsTruncated) {
		            diagnostics.push({
		                severity: 'warning',
		                code: 'decodedPageObjectsTruncated',
		                message: `page stream truncated: kept ${stream.decodedPageObjects.length} of ${Object.values(stream.decodedPageObjectCounts ?? {}).reduce((sum, count) => sum + count, 0)} decoded object(s); port-instance enrichment skipped`,
		                streamPath: stream.path,
		            });
		            continue;
		        }
		        for (const pageObject of stream.decodedPageObjects) {
		            if (pageObject.kind === 'drawn-inst') {
		                enrichDrawnInstMetadata(pageObject, hierarchyLookup, stream.parsed?.kind === 'page' ? stream.parsed.viewName : '');
		                continue;
		            }
		            if (pageObject.kind !== 'placed-symbol') {
		                continue;
		            }
		            enrichPlacedSymbolMetadata(pageObject, diagnostics, stream.path);
		            const symbolPins = findPlacedSymbolPins(pageObject, symbolPinLookup);
		            if (!symbolPins) {
		                pageObject.materializedPortInstances = (pageObject.portInstances ?? []).map(materializedPortFromExplicitPort);
		                continue;
		            }
		            pageObject.symbolPins = symbolPins;
		            const existingPortInstancesByPosition = new Map();
		            for (const portInstance of pageObject.portInstances ?? []) {
		                existingPortInstancesByPosition.set(portInstance.position, portInstance);
		            }
		            for (let position = 0; position < symbolPins.length; position++) {
		                const symbolPin = symbolPins[position];
		                const existingPortInstance = existingPortInstancesByPosition.get(position);
		                const metadataMatches = existingPortInstance ? applySymbolPinMetadata(existingPortInstance, symbolPin) : true;
		                if (!metadataMatches) {
		                    const symbols = uncorroboratedSymbolNames.get(stream.path) ?? new Set();
		                    symbols.add(pageObject.symbolName);
		                    uncorroboratedSymbolNames.set(stream.path, symbols);
		                }
		            }
		            const positions = new Set(symbolPins.map((_, position) => position));
		            for (const portInstance of pageObject.portInstances ?? []) {
		                positions.add(portInstance.position);
		            }
		            pageObject.materializedPortInstances = Array.from(positions)
		                .sort((left, right) => left - right)
		                .map((position) => {
		                const existingPortInstance = existingPortInstancesByPosition.get(position);
		                if (existingPortInstance) {
		                    return materializedPortFromExplicitPort(existingPortInstance);
		                }
		                return materializedPortFromSymbolPin(symbolPins[position], position);
		            });
		        }
		    }
		    for (const [streamPath, symbolNames] of uncorroboratedSymbolNames) {
		        const names = [...symbolNames].sort();
		        diagnostics.push({
		            severity: 'info',
		            code: 'placed-pin-definition-name-mismatch',
		            message: `page port records disagree with the symbol definition's pin names on ${names.length} symbol(s) (${names.join(', ')}); those ports take no pin type from the definition`,
		            streamPath,
		        });
		    }
		}
		function materializeOrCadDsnCfbDocument(entries, streams, hierarchyOccurrenceTrust = (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)({ streams })) {
		    const diagnostics = [];
		    const stringCatalog = libraryStringCatalogFromStreams(streams);
		    if (stringCatalog) {
		        const referencedNames = enrichPageObjectsFromStringCatalog(streams, stringCatalog, diagnostics);
		        applyLibraryPropertyNames(streams, referencedNames);
		    }
		    enrichPagePortInstancesFromSymbolPins(streams, diagnostics, hierarchyOccurrenceTrust);
		    const entriesWithoutContent = entries.map(({ content, ...entry }) => entry);
		    const storages = entriesWithoutContent.filter((entry) => entry.type === 1 || entry.type === 5);
		    const schematicViews = streams
		        .map((stream) => stream.parsed)
		        .filter((parsed) => parsed?.kind === 'schematic');
		    const paths = entries.map((entry) => OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.stripRoot(entry.path));
		    return {
		        entries: entriesWithoutContent,
		        streams,
		        hierarchyOccurrenceTrust,
		        storages,
		        schematicViews,
		        summary: {
		            entryCount: entries.length,
		            streamCount: streams.length,
		            storageCount: storages.length,
		            viewCount: entries.filter((entry) => /^Root Entry\/Views\/[^/]+\/$/.test(entry.path)).length,
		            pageStreamCount: paths.filter((path) => /^Views\/[^/]+\/Pages\/[^/]+$/.test(path)).length,
		            packageStreamCount: paths.filter((path) => /^Packages\/[^/]+$/.test(path)).length,
		            symbolStreamCount: paths.filter((path) => /^Symbols\/[^/]+$/.test(path)).length,
		        },
		        ...(diagnostics.length > 0 ? { diagnostics } : {}),
		    };
		}
		
	} (OrcadDsnCfbMaterializer));
	return OrcadDsnCfbMaterializer;
}

var OrcadDsnCfbStreamParsing = {};

var OrcadDsnCfbRecordDecoders = {};

var OrcadDsnCfbPagePropertyFrames = {};

var hasRequiredOrcadDsnCfbPagePropertyFrames;

function requireOrcadDsnCfbPagePropertyFrames () {
	if (hasRequiredOrcadDsnCfbPagePropertyFrames) return OrcadDsnCfbPagePropertyFrames;
	hasRequiredOrcadDsnCfbPagePropertyFrames = 1;
	Object.defineProperty(OrcadDsnCfbPagePropertyFrames, "__esModule", { value: true });
	OrcadDsnCfbPagePropertyFrames.readPagePropertyFrame = readPagePropertyFrame;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	function readPagePropertyFrame(payload, kind, report) {
	    const candidates = [];
	    for (let offset = 0; offset + 12 <= payload.length; offset++) {
	        if (payload[offset] !== kind || payload[offset + 9] !== kind) {
	            continue;
	        }
	        const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(payload, offset + 10);
	        const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload, offset + 1);
	        if (offset + length + 1 === payload.length && length !== 11 + count * 8) {
	            report(`property-frame-count-mismatch offset=${offset} kind=${kind} length=${length} count=${count}`);
	        }
	        if (length !== 11 + count * 8 || offset + length + 1 !== payload.length) {
	            continue;
	        }
	        const pairs = [];
	        for (let index = 0; index < count; index++) {
	            pairs.push({
	                nameId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload, offset + 12 + index * 8),
	                valueId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload, offset + 16 + index * 8),
	            });
	        }
	        candidates.push(pairs);
	    }
	    if (candidates.length > 1) {
	        report(`property-frame-ambiguous kind=${kind} candidates=${candidates.length}`);
	    }
	    return candidates.length === 1 ? candidates[0] : undefined;
	}
	
	return OrcadDsnCfbPagePropertyFrames;
}

var OrcadDsnCfbTypedObjectFrames = {};

var hasRequiredOrcadDsnCfbTypedObjectFrames;

function requireOrcadDsnCfbTypedObjectFrames () {
	if (hasRequiredOrcadDsnCfbTypedObjectFrames) return OrcadDsnCfbTypedObjectFrames;
	hasRequiredOrcadDsnCfbTypedObjectFrames = 1;
	Object.defineProperty(OrcadDsnCfbTypedObjectFrames, "__esModule", { value: true });
	OrcadDsnCfbTypedObjectFrames.TypedObjectFrameRole = void 0;
	OrcadDsnCfbTypedObjectFrames.findCrossKindFrameConflicts = findCrossKindFrameConflicts;
	OrcadDsnCfbTypedObjectFrames.readTypedObjectFrames = readTypedObjectFrames;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	var TypedObjectFrameRole;
	(function (TypedObjectFrameRole) {
	    TypedObjectFrameRole["Owner"] = "owner";
	    TypedObjectFrameRole["Pin"] = "pin";
	    TypedObjectFrameRole["Display"] = "display";
	})(TypedObjectFrameRole || (OrcadDsnCfbTypedObjectFrames.TypedObjectFrameRole = TypedObjectFrameRole = {}));
	function findCrossKindFrameConflicts(groups, diagnostics, streamPath = '') {
	    const claims = groups
	        .flatMap(({ frames, role }, group) => [...frames.values()].map((frame) => ({ frame, group, role })))
	        .toSorted((left, right) => left.frame.offset - right.frame.offset);
	    const conflicts = new Set();
	    let active = [];
	    for (const claim of claims) {
	        active = active.filter((previous) => previous.frame.endOffset > claim.frame.offset);
	        for (const previous of active) {
	            const acceptsChild = (previous.role === TypedObjectFrameRole.Owner &&
	                (claim.role === TypedObjectFrameRole.Pin || claim.role === TypedObjectFrameRole.Display)) ||
	                (previous.role === TypedObjectFrameRole.Pin && claim.role === TypedObjectFrameRole.Display);
	            if (previous.group === claim.group ||
	                (acceptsChild &&
	                    claim.frame.offset >= previous.frame.dataOffset &&
	                    claim.frame.endOffset <= previous.frame.endOffset)) {
	                continue;
	            }
	            conflicts.add(previous.frame.dataOffset);
	            conflicts.add(claim.frame.dataOffset);
	            diagnostics.push({
	                severity: 'warning',
	                code: 'typed-frame-cross-kind-overlap',
	                streamPath,
	                offset: claim.frame.dataOffset,
	                message: `CFB typed frames [${previous.frame.offset},${previous.frame.endOffset}) and [${claim.frame.offset},${claim.frame.endOffset}) overlap without a complete owner-child boundary; both withheld`,
	            });
	        }
	        active.push(claim);
	    }
	    return conflicts;
	}
	function readTypedObjectFrames(bytes, kinds, baseDepth, diagnostics = [], streamPath = '') {
	    const frames = new Map();
	    const conflicts = new Set();
	    const baseHeaders = new Set();
	    const tails = new Map();
	    const headerEnd = (offset) => {
	        if (offset + 9 > bytes.length ||
	            !kinds.includes(bytes[offset]) ||
	            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5) !== 0) {
	            return undefined;
	        }
	        const endOffset = offset + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
	        return endOffset > offset + 9 && endOffset <= bytes.length ? endOffset : undefined;
	    };
	    const readTail = (offset) => {
	        const path = [];
	        let current = offset;
	        let tail;
	        while (true) {
	            if (tails.has(current)) {
	                tail = tails.get(current);
	                break;
	            }
	            const currentEnd = headerEnd(current);
	            if (currentEnd === undefined) {
	                tails.set(current, undefined);
	                break;
	            }
	            const cursor = current + 9;
	            const nestedEnd = headerEnd(cursor);
	            if (nestedEnd !== undefined && nestedEnd <= currentEnd) {
	                path.push({ headerEndOffset: currentEnd, offset: current });
	                current = cursor;
	                continue;
	            }
	            if (cursor + 3 > currentEnd || bytes[cursor] !== bytes[current]) {
	                tails.set(current, undefined);
	                break;
	            }
	            const storedCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 1);
	            const count = storedCount === 0xffff ? 0 : storedCount;
	            const terminator = cursor + 3 + count * 8;
	            if (terminator + 4 > currentEnd ||
	                !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator, [0xff, 0xe4, 0x5c, 0x39])) {
	                tails.set(current, undefined);
	                break;
	            }
	            const remaining = currentEnd - terminator;
	            const hasContinuation = remaining > 8 && terminator + 8 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, terminator + 4) === currentEnd;
	            if (remaining !== 4 && remaining !== 8 && !hasContinuation) {
	                tails.set(current, undefined);
	                break;
	            }
	            tail = {
	                dataOffset: terminator + (remaining === 8 ? 8 : 4),
	                depth: 1,
	                headerEndOffset: currentEnd,
	                ...(remaining === 8 && !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator + 4, [0, 0, 0, 0]) ?
	                    { reservedTerminatorOffset: terminator }
	                    : {}),
	            };
	            tails.set(current, tail);
	            break;
	        }
	        while (path.length > 0) {
	            const parent = path.pop();
	            if (parent === undefined || tail === undefined || tail.headerEndOffset > parent.headerEndOffset) {
	                tail = undefined;
	                if (parent !== undefined) {
	                    tails.set(parent.offset, undefined);
	                }
	                continue;
	            }
	            tail = {
	                dataOffset: tail.dataOffset,
	                depth: tail.depth + 1,
	                headerEndOffset: parent.headerEndOffset,
	                nextHeaderOffset: current,
	                ...(tail.reservedTerminatorOffset === undefined ?
	                    {}
	                    : { reservedTerminatorOffset: tail.reservedTerminatorOffset }),
	            };
	            tails.set(parent.offset, tail);
	            current = parent.offset;
	        }
	        return tails.get(offset);
	    };
	    for (let offset = 0; offset + 9 <= bytes.length; offset++) {
	        if (baseHeaders.has(offset)) {
	            continue;
	        }
	        const endOffset = headerEnd(offset);
	        if (endOffset === undefined) {
	            continue;
	        }
	        const tail = readTail(offset + 9);
	        if (tail === undefined ||
	            tail.headerEndOffset > endOffset ||
	            (baseDepth !== undefined && tail.depth !== baseDepth) ||
	            tail.dataOffset >= endOffset) {
	            continue;
	        }
	        if (tail.reservedTerminatorOffset !== undefined) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'typed-frame-terminator-unverified',
	                streamPath,
	                offset: tail.reservedTerminatorOffset,
	                message: `CFB typed frame at ${offset}: declared eight-byte terminator has nonzero reserved bytes; frame withheld`,
	            });
	            continue;
	        }
	        for (let header = offset + 9;;) {
	            baseHeaders.add(header);
	            const nestedTail = tails.get(header);
	            if (nestedTail?.nextHeaderOffset === undefined) {
	                break;
	            }
	            header = nestedTail.nextHeaderOffset;
	        }
	        const frame = { offset, dataOffset: tail.dataOffset, endOffset };
	        if (frames.has(frame.dataOffset)) {
	            conflicts.add(frame.dataOffset);
	            diagnostics.push({
	                severity: 'warning',
	                code: 'typed-frame-data-offset-collision',
	                streamPath,
	                offset: frame.dataOffset,
	                message: `CFB typed frames at ${frames.get(frame.dataOffset)?.offset} and ${frame.offset} claim one data offset; both withheld`,
	            });
	        }
	        else {
	            frames.set(frame.dataOffset, frame);
	        }
	    }
	    for (const offset of conflicts) {
	        frames.delete(offset);
	    }
	    return frames;
	}
	
	return OrcadDsnCfbTypedObjectFrames;
}

var OrcadDsnCfbStreamSpecs = {};

var hasRequiredOrcadDsnCfbStreamSpecs;

function requireOrcadDsnCfbStreamSpecs () {
	if (hasRequiredOrcadDsnCfbStreamSpecs) return OrcadDsnCfbStreamSpecs;
	hasRequiredOrcadDsnCfbStreamSpecs = 1;
	Object.defineProperty(OrcadDsnCfbStreamSpecs, "__esModule", { value: true });
	OrcadDsnCfbStreamSpecs.ORCAD_DSN_CFB_STREAM_SPECS = void 0;
	OrcadDsnCfbStreamSpecs.ORCAD_DSN_CFB_STREAM_SPECS = [
	    { name: 'dsn-stream', path: /^Root Entry\/DsnStream$/, framing: 'json', records: [] },
	    { name: 'library', path: /^Root Entry\/Library$/, framing: 'whole-stream', records: [] },
	    { name: 'parts-directory', path: /^Root Entry\/Parts Directory$/, framing: 'whole-stream', records: [] },
	    { name: 'packages-directory', path: /^Root Entry\/Packages Directory$/, framing: 'whole-stream', records: [] },
	    { name: 'schematic', path: /^Root Entry\/Views\/[^/]+\/Schematic$/, framing: 'whole-stream', records: [] },
	    {
	        name: 'hierarchy',
	        path: /^Root Entry\/Views\/[^/]+\/Hierarchy\/Hierarchy$/,
	        framing: 'terminated-records',
	        records: [],
	    },
	    {
	        name: 'cis-schematic-info',
	        path: /^Root Entry\/Views\/[^/]+\/CISSchematic\/SchematicInfoStorage\/[^/]+$/,
	        framing: 'whole-stream',
	        records: [],
	    },
	    { name: 'variant-text', path: (entry) => entry.path.includes('/VariantStore/'), framing: 'text', records: [] },
	    { name: 'cache', path: /^Root Entry\/Cache$/, framing: 'terminated-records', records: [] },
	    { name: 'package', path: /^Root Entry\/Packages\/[^/]+$/, framing: 'terminated-records', records: [] },
	    { name: 'symbol', path: /^Root Entry\/Symbols\/[^/]+$/, framing: 'terminated-records', records: [] },
	    { name: 'page', path: /^Root Entry\/Views\/[^/]+\/Pages\/[^/]+$/, framing: 'page-records', records: [] },
	    { name: 'text', path: /\.txt$/i, framing: 'text', records: [] },
	];
	
	return OrcadDsnCfbStreamSpecs;
}

var hasRequiredOrcadDsnCfbRecordDecoders;

function requireOrcadDsnCfbRecordDecoders () {
	if (hasRequiredOrcadDsnCfbRecordDecoders) return OrcadDsnCfbRecordDecoders;
	hasRequiredOrcadDsnCfbRecordDecoders = 1;
	Object.defineProperty(OrcadDsnCfbRecordDecoders, "__esModule", { value: true });
	OrcadDsnCfbRecordDecoders.OrcadDsnCfbRecordDecoders = void 0;
	const DsnCfbBinarySpec_1 = requireDsnCfbBinarySpec();
	const OrcadDsnCfbPagePropertyFrames_1 = requireOrcadDsnCfbPagePropertyFrames();
	const OrcadDsnCfbTypedObjectFrames_1 = requireOrcadDsnCfbTypedObjectFrames();
	const OrcadDsnCfbDeclarativeSpecs_1 = requireOrcadDsnCfbDeclarativeSpecs();
	const OrcadDsnCfbStreamSpecs_1 = requireOrcadDsnCfbStreamSpecs();
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
	var OrcadDsnCfbRecordDecoders$1;
	(function (OrcadDsnCfbRecordDecoders) {
	    const CFB_STREAM_ENTRY_TYPE = 2;
	    const CACHE_STREAM_SPEC_NAME = 'cache';
	    const SOURCE_LIBRARY_PATH_PATTERN = /\.(?:OLB|DSN)$/i;
	    function shouldIncludeRawRecords(options) {
	        return options.includeRawRecords === true;
	    }
	    function shouldIncludeDiagnostics(options) {
	        return options.includeDiagnostics === true || options.includeRawRecords === true;
	    }
	    function withLibraryStringCatalog(options) {
	        return {
	            ...options,
	            extra: {
	                ...options.extra,
	                libraryStringCatalog: options.libraryStringCatalog,
	            },
	        };
	    }
	    function createPrimitiveCounts() {
	        return {
	            'comment-text': 0,
	            rect: 0,
	            ellipse: 0,
	            polygon: 0,
	            polyline: 0,
	            arc: 0,
	            line: 0,
	            bitmap: 0,
	        };
	    }
	    function createPageObjectCounts() {
	        return {
	            wire: 0,
	            alias: 0,
	            'placed-symbol': 0,
	            'drawn-inst': 0,
	            'drawn-port': 0,
	            'display-property': 0,
	            'pin-to-wire': 0,
	            'port-instance': 0,
	            'bus-entry': 0,
	            junction: 0,
	        };
	    }
	    function createSymbolObjectCounts() {
	        return {
	            'view-header': 0,
	            'symbol-pin': 0,
	            'display-property': 0,
	            'part-value': 0,
	            'pin-number-map': 0,
	            'named-record': 0,
	        };
	    }
	    function hasDecodedPrimitives(counts) {
	        return Object.values(counts).some((count) => count > 0);
	    }
	    OrcadDsnCfbRecordDecoders.hasDecodedPrimitives = hasDecodedPrimitives;
	    function hasDecodedPageObjects(counts) {
	        return Object.values(counts).some((count) => count > 0);
	    }
	    OrcadDsnCfbRecordDecoders.hasDecodedPageObjects = hasDecodedPageObjects;
	    function hasDecodedSymbolObjects(counts) {
	        return Object.values(counts).some((count) => count > 0);
	    }
	    OrcadDsnCfbRecordDecoders.hasDecodedSymbolObjects = hasDecodedSymbolObjects;
	    function decodePrimitiveRecordNode(bytes, offset, options = {}) {
	        for (const spec of OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PRIMITIVE_RECORD_SPECS) {
	            const decoded = DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
	                bytes,
	                offset,
	                streamPath: options.streamPath ?? '',
	                streamName: options.streamName,
	            });
	            if (decoded) {
	                return decoded;
	            }
	        }
	        return undefined;
	    }
	    function compactPolygonDiagnostic(bytes, offset, options) {
	        if (bytes[offset] !== 0x2c ||
	            bytes[offset + 1] !== 0x2c ||
	            (bytes[offset + 2] !== 0x2e && bytes[offset + 2] !== 0x2a)) {
	            return undefined;
	        }
	        const base = {
	            severity: 'warning',
	            specName: 'primitive.polygon',
	            streamPath: options.streamPath ?? '',
	            offset,
	        };
	        if (offset + 28 > bytes.length) {
	            return { ...base, code: 'compact-polygon-truncated-header', message: 'Compact polygon header is truncated' };
	        }
	        const pointCount = bytes[offset + 26] | (bytes[offset + 27] << 8);
	        if (bytes[offset + 2] === 0x2a &&
	            (pointCount !== 4 ||
	                [10, 14, 18, 22].some((field) => OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + field) !== 0))) {
	            return {
	                ...base,
	                code: 'compact-polygon-layout-unverified',
	                message: 'Four-point polygon count or style fields lack a corroborated layout',
	                details: { pointCount },
	            };
	        }
	        if (pointCount < 2 || pointCount > 4096) {
	            return {
	                ...base,
	                code: 'compact-polygon-impossible-point-count',
	                message: `Compact polygon has impossible point count ${pointCount}`,
	                details: { pointCount },
	            };
	        }
	        const terminatorOffset = offset + 28 + pointCount * 4;
	        if (terminatorOffset + 8 > bytes.length) {
	            return {
	                ...base,
	                code: 'compact-polygon-truncated-points',
	                message: `Compact polygon point payload is truncated for ${pointCount} points`,
	                details: { pointCount },
	            };
	        }
	        const terminator = [0xff, 0xe4, 0x5c, 0x39, 0x00, 0x00, 0x00, 0x00];
	        if (!terminator.every((byte, index) => bytes[terminatorOffset + index] === byte)) {
	            return {
	                ...base,
	                code: 'compact-polygon-invalid-terminator',
	                message: 'Compact polygon has an invalid record terminator',
	                details: { pointCount },
	            };
	        }
	        return {
	            ...base,
	            code: 'compact-polygon-invalid-coordinates',
	            message: 'Compact polygon failed validation',
	            details: { pointCount },
	        };
	    }
	    function decodeOrCadDsnCfbPrimitiveRecords(bytes, options = {}) {
	        const maxRecords = options.maxRecords ?? Number.MAX_SAFE_INTEGER;
	        const records = [];
	        const decodedRecords = [];
	        const diagnostics = [];
	        const counts = createPrimitiveCounts();
	        let truncated = false;
	        let decodedByteCount = 0;
	        for (let offset = 0; offset < bytes.length; offset++) {
	            const decoded = decodePrimitiveRecordNode(bytes, offset, options);
	            if (!decoded) {
	                if (shouldIncludeDiagnostics(options)) {
	                    const diagnostic = compactPolygonDiagnostic(bytes, offset, options);
	                    if (diagnostic) {
	                        diagnostics.push(diagnostic);
	                    }
	                }
	                continue;
	            }
	            const record = decoded.value;
	            counts[record.kind]++;
	            decodedByteCount += record.byteLength;
	            if (records.length < maxRecords) {
	                records.push(record);
	                if (shouldIncludeRawRecords(options)) {
	                    decodedRecords.push(decoded);
	                }
	            }
	            else {
	                truncated = true;
	            }
	            if (shouldIncludeDiagnostics(options)) {
	                diagnostics.push(...decoded.diagnostics);
	            }
	            offset += record.byteLength - 1;
	        }
	        return {
	            records,
	            counts,
	            truncated,
	            decodedByteCount,
	            ...(shouldIncludeRawRecords(options) ? { decodedRecords } : {}),
	            ...(shouldIncludeDiagnostics(options) ? { diagnostics } : {}),
	        };
	    }
	    OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords = decodeOrCadDsnCfbPrimitiveRecords;
	    function junctionPageObjectsFromWire(wire) {
	        return (wire.junctions ?? []).map((junction) => ({
	            kind: 'junction',
	            offset: wire.offset,
	            byteLength: wire.byteLength,
	            payloadByteLength: wire.payloadByteLength,
	            wireDbId: wire.dbId,
	            endpoint: junction.endpoint,
	            locX: junction.locX,
	            locY: junction.locY,
	        }));
	    }
	    function decodePageRecordSpecNode(spec, record, options = {}) {
	        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
	            bytes: record.payload,
	            offset: 0,
	            streamPath: options.streamPath ?? '',
	            streamName: options.streamName,
	            payloadOffset: record.offset,
	            payloadByteLength: record.payloadByteLength,
	            byteLength: record.byteLength,
	            extra: options.extra,
	        });
	    }
	    function derivedPageRecordNode(kind, value, record, options = {}) {
	        return {
	            kind,
	            family: 'derived',
	            value,
	            span: {
	                streamPath: options.streamPath ?? '',
	                offset: value.offset,
	                byteLength: value.byteLength,
	                payloadOffset: record.offset,
	                payloadByteLength: record.payloadByteLength,
	            },
	            specName: `page.derived-${kind}`,
	            confidence: 'derived',
	            diagnostics: [],
	        };
	    }
	    function scanDrawnInstPageObjectNodes(record, options = {}) {
	        const records = [];
	        for (let offset = 0; offset + 24 <= record.payload.length; offset++) {
	            for (const spec of OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_SCAN_RECORD_SPECS) {
	                const decoded = DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
	                    bytes: record.payload,
	                    offset,
	                    streamPath: options.streamPath ?? '',
	                    streamName: options.streamName,
	                    payloadOffset: record.offset + offset,
	                    extra: options.extra,
	                });
	                if (!decoded) {
	                    continue;
	                }
	                records.push(decoded);
	                offset += 23;
	                break;
	            }
	        }
	        return records;
	    }
	    function scanEmbeddedWirePageObjectNode(record, options = {}) {
	        const innerOffset = 43;
	        if (record.payload.length < innerOffset + 52 ||
	            !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(record.payload, 0, [0x27, 0x00, 0x00, 0x00, 0x27, 0x00, 0x00, 0x00])) {
	            return undefined;
	        }
	        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS[0], {
	            bytes: record.payload,
	            offset: innerOffset,
	            streamPath: options.streamPath ?? '',
	            streamName: options.streamName,
	            payloadOffset: record.offset + innerOffset,
	            payloadByteLength: record.payloadByteLength - innerOffset,
	            byteLength: record.byteLength - innerOffset,
	        });
	    }
	    function decodePageObjectRecordNodes(record, options = {}) {
	        const wire = decodePageRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS[0], record, options);
	        if (wire) {
	            const junctions = junctionPageObjectsFromWire(wire.value).map((junction) => derivedPageRecordNode('junction', junction, record, options));
	            const placedSymbol = decodePageRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS[3], record, options);
	            return [wire, ...junctions, ...(placedSymbol ? [placedSymbol] : [])];
	        }
	        const decoded = OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS.slice(1)
	            .map((spec) => decodePageRecordSpecNode(spec, record, options))
	            .filter((pageObject) => Boolean(pageObject));
	        if (decoded.some((pageObject) => pageObject.kind === 'placed-symbol')) {
	            return decoded;
	        }
	        const embeddedWire = scanEmbeddedWirePageObjectNode(record, options);
	        return [...decoded, ...(embeddedWire ? [embeddedWire] : []), ...scanDrawnInstPageObjectNodes(record, options)];
	    }
	    function attachDisplayPropertyToPlacedSymbol(placedSymbol, displayProperty) {
	        placedSymbol.displayProperties ??= [];
	        placedSymbol.displayProperties.push(displayProperty);
	        if (displayProperty.displayedValue === undefined || displayProperty.displayedValue.length === 0) {
	            return;
	        }
	        placedSymbol.instanceProperties ??= [];
	        placedSymbol.instanceProperties.push({
	            name: displayProperty.propertyName,
	            value: displayProperty.displayedValue,
	            propertyCode: displayProperty.propertyCode,
	            source: 'display-property',
	        });
	    }
	    function attachPortInstanceToPlacedSymbol(placedSymbol, portInstance) {
	        placedSymbol.portInstances ??= [];
	        placedSymbol.portInstances.push(portInstance);
	    }
	    function attachDisplayPropertyToDrawnInst(drawnInst, displayProperty) {
	        drawnInst.displayProperties ??= [];
	        drawnInst.displayProperties.push(displayProperty);
	    }
	    function attachDrawnPortToDrawnInst(drawnInst, drawnPort) {
	        drawnInst.portInstances ??= [];
	        drawnInst.portInstances.push(drawnPort);
	    }
	    function attachPinToWireToPlacedSymbol(placedSymbol, pinToWire) {
	        placedSymbol.connectedPins ??= [];
	        placedSymbol.connectedPins.push(pinToWire);
	    }
	    function attachOwnerToPlacedSymbolChild(record, placedSymbol) {
	        if (!placedSymbol) {
	            return record;
	        }
	        return {
	            ...record,
	            ownerDbId: placedSymbol.dbId,
	            ownerSymbolName: placedSymbol.symbolName,
	            ownerRole: placedSymbol.role,
	        };
	    }
	    function attachOwnerToDrawnInstPort(record, drawnInst) {
	        if (!drawnInst) {
	            return record;
	        }
	        return {
	            ...record,
	            ownerDbId: drawnInst.dbId,
	            ownerRole: 'drawn-inst',
	        };
	    }
	    function decodeOrCadDsnCfbPageObjects(bytes, options = {}) {
	        const resolvedOptions = withLibraryStringCatalog(options);
	        const diagnostics = [];
	        const pinCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x10, 0x11], 1, diagnostics, resolvedOptions.streamPath);
	        const ownerCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x0d, 0x25], undefined, diagnostics, resolvedOptions.streamPath);
	        const drawnCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x0c], undefined, diagnostics, resolvedOptions.streamPath);
	        const displayCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x27], 1, diagnostics, resolvedOptions.streamPath);
	        const conflictedDataOffsets = new Set([
	            ...(0, OrcadDsnCfbTypedObjectFrames_1.findCrossKindFrameConflicts)([
	                { frames: pinCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Pin },
	                { frames: ownerCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Owner },
	                { frames: drawnCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Owner },
	                { frames: displayCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Display },
	            ], diagnostics, options.streamPath),
	            ...diagnostics
	                .filter((diagnostic) => diagnostic.code === 'typed-frame-data-offset-collision')
	                .map((diagnostic) => diagnostic.offset),
	        ]);
	        const typedPins = new Map([...pinCandidates].filter(([offset]) => !conflictedDataOffsets.has(offset)));
	        const typedOwners = new Map([...ownerCandidates].filter(([offset]) => !conflictedDataOffsets.has(offset)));
	        const drawnOwners = [...drawnCandidates.values()].filter((frame) => !conflictedDataOffsets.has(frame.dataOffset));
	        const displayDataFrames = new Map([...displayCandidates].filter(([offset]) => !conflictedDataOffsets.has(offset)));
	        const displayFrames = new Map([...displayDataFrames.values()].map((frame) => [frame.offset, frame]));
	        const placementDisplayCounts = new Map();
	        const maxRecords = resolvedOptions.maxRecords ?? Number.MAX_SAFE_INTEGER;
	        const records = [];
	        const decodedRecords = [];
	        const counts = createPageObjectCounts();
	        let truncated = false;
	        let decodedByteCount = 0;
	        let currentPlacedSymbol;
	        let currentDrawnInst;
	        let currentDrawnFrame;
	        let pendingPropertyPairsPayload;
	        let pendingAliasOwner;
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachPagePayload(bytes, (payload) => {
	            if (conflictedDataOffsets.has(payload.offset)) {
	                diagnostics.push({
	                    severity: 'warning',
	                    code: 'typed-frame-owner-context-withheld',
	                    streamPath: options.streamPath ?? '',
	                    offset: payload.offset,
	                    message: `CFB page ownership: conflicting payload interrupts placement=${currentPlacedSymbol?.dbId}, drawing=${currentDrawnInst?.dbId}, wire=${pendingAliasOwner}; following children cannot inherit these owners`,
	                });
	                currentPlacedSymbol = undefined;
	                currentDrawnInst = undefined;
	                currentDrawnFrame = undefined;
	                pendingAliasOwner = undefined;
	                pendingPropertyPairsPayload = undefined;
	                return;
	            }
	            const reportPropertyFrame = (reason) => {
	                diagnostics.push({
	                    severity: 'warning',
	                    code: 'page-property-frame-withheld',
	                    streamPath: options.streamPath ?? '',
	                    offset: payload.offset,
	                    message: `CFB page property [page-property-frame-withheld] ${reason}`,
	                });
	            };
	            const aliasOwner = pendingAliasOwner;
	            pendingAliasOwner = undefined;
	            const pinFrame = typedPins.get(payload.offset);
	            const ownerFrame = currentPlacedSymbol === undefined ? currentDrawnFrame : typedOwners.get(currentPlacedSymbol.offset);
	            let ownsPin = pinFrame !== undefined &&
	                ownerFrame !== undefined &&
	                pinFrame.offset >= ownerFrame.dataOffset &&
	                pinFrame.endOffset <= ownerFrame.endOffset &&
	                pinFrame.dataOffset + 16 <= pinFrame.endOffset;
	            if (ownsPin && pinFrame) {
	                const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, pinFrame.dataOffset + 14);
	                let cursor = pinFrame.dataOffset + 16;
	                for (let index = 0; index < count; index++) {
	                    const child = displayFrames.get(cursor);
	                    if (!child || child.endOffset > pinFrame.endOffset) {
	                        ownsPin = false;
	                        break;
	                    }
	                    cursor = child.endOffset;
	                }
	                ownsPin = ownsPin && cursor === pinFrame.endOffset;
	            }
	            if (pinFrame && !ownsPin) {
	                diagnostics.push({
	                    severity: 'warning',
	                    code: 'page-pin-frame-withheld',
	                    offset: payload.offset,
	                    streamPath: options.streamPath ?? '',
	                    message: `CFB page pin [page-pin-frame-withheld] offset=${payload.offset}: incomplete counted children or missing framed placement owner`,
	                });
	            }
	            const decodedNodes = decodePageObjectRecordNodes(payload, {
	                ...resolvedOptions,
	                extra: {
	                    ...resolvedOptions.extra,
	                    ownedConnectivityFrame: ownsPin,
	                    placementDisplayCounts,
	                    displayFrameEnd: displayDataFrames.get(payload.offset)?.endOffset,
	                },
	            });
	            if (aliasOwner !== undefined &&
	                !decodedNodes.some((node) => node.value.kind === 'alias' && node.value.offset === payload.offset)) {
	                diagnostics.push({
	                    severity: 'warning',
	                    code: 'wire-alias-child-unresolved',
	                    streamPath: options.streamPath ?? '',
	                    offset: payload.offset,
	                    message: `CFB alias [wire-alias-child-unresolved] wire=${aliasOwner}: counted child is not a decoded alias`,
	                });
	            }
	            if (decodedNodes.length === 0) {
	                pendingPropertyPairsPayload = payload.payload;
	                return;
	            }
	            decodedByteCount += payload.byteLength;
	            let closesCurrentPlacedSymbol = false;
	            let closesCurrentDrawnInst = false;
	            for (const decodedNode of decodedNodes) {
	                const decodedRecord = decodedNode.value;
	                const record = decodedRecord.kind === 'pin-to-wire' || decodedRecord.kind === 'port-instance' ?
	                    attachOwnerToPlacedSymbolChild(decodedRecord, currentPlacedSymbol)
	                    : decodedRecord.kind === 'drawn-port' ? attachOwnerToDrawnInstPort(decodedRecord, currentDrawnInst)
	                        : decodedRecord;
	                if (currentPlacedSymbol && record.kind === 'display-property') {
	                    attachDisplayPropertyToPlacedSymbol(currentPlacedSymbol, record);
	                }
	                if (currentDrawnInst && record.kind === 'display-property') {
	                    attachDisplayPropertyToDrawnInst(currentDrawnInst, record);
	                }
	                if (currentPlacedSymbol && record.kind === 'port-instance') {
	                    attachPortInstanceToPlacedSymbol(currentPlacedSymbol, record);
	                }
	                if (currentDrawnInst && record.kind === 'drawn-port') {
	                    attachDrawnPortToDrawnInst(currentDrawnInst, record);
	                }
	                if (currentPlacedSymbol && record.kind === 'pin-to-wire') {
	                    attachPinToWireToPlacedSymbol(currentPlacedSymbol, record);
	                }
	                counts[record.kind]++;
	                if (records.length < maxRecords) {
	                    records.push(record);
	                    if (shouldIncludeRawRecords(options)) {
	                        decodedRecords.push({
	                            ...decodedNode,
	                            value: record,
	                        });
	                    }
	                }
	                else {
	                    truncated = true;
	                }
	                if (shouldIncludeDiagnostics(options)) {
	                    diagnostics.push(...decodedNode.diagnostics);
	                }
	                if (record.kind === 'placed-symbol') {
	                    const frame = typedOwners.get(record.offset);
	                    if (frame) {
	                        record.recordFrame = { offset: frame.offset, endOffset: frame.endOffset };
	                        const countOffset = placementDisplayCounts.get(record.offset);
	                        if (record.role === 'part-inst' && countOffset !== undefined) {
	                            let cursor = countOffset + 2;
	                            let valid = cursor <= frame.endOffset;
	                            const count = valid ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, countOffset) : 0;
	                            for (let index = 0; valid && index < count; index++) {
	                                const child = displayFrames.get(cursor);
	                                if (!child || child.endOffset > frame.endOffset) {
	                                    valid = false;
	                                }
	                                else {
	                                    cursor = child.endOffset;
	                                }
	                            }
	                            const length = cursor + 3 <= frame.endOffset ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 1) : 0;
	                            const end = cursor + 3 + length;
	                            const valueStringIndex = end + 5 <= frame.endOffset ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, end + 1) : undefined;
	                            if (valid &&
	                                bytes[cursor] === 0x18 &&
	                                bytes[end] === 0 &&
	                                !bytes.subarray(cursor + 3, end).some((byte) => byte < 0x20 || byte > 0x7e) &&
	                                valueStringIndex !== undefined) {
	                                record.referenceTail = {
	                                    offset: cursor + 1,
	                                    reference: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, cursor + 3, end),
	                                    valueStringIndex,
	                                };
	                                if (valueStringIndex > 0 && options.libraryStringCatalog?.[valueStringIndex] === undefined) {
	                                    record.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
	                                    record.valueState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld;
	                                    diagnostics.push({
	                                        severity: 'warning',
	                                        code: 'placement-value-withheld',
	                                        offset: end + 1,
	                                        streamPath: options.streamPath ?? '',
	                                        message: `CFB placement value: catalogue index ${valueStringIndex} does not resolve; value withheld`,
	                                    });
	                                    diagnostics.push({
	                                        severity: 'warning',
	                                        code: 'placement-reference-tail-withheld',
	                                        offset: cursor,
	                                        streamPath: options.streamPath ?? '',
	                                        message: `CFB placement reference: tail catalogue index ${valueStringIndex} does not resolve; reference and connectivity withheld`,
	                                    });
	                                }
	                            }
	                            else {
	                                record.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
	                                diagnostics.push({
	                                    severity: 'warning',
	                                    code: 'placement-reference-tail-withheld',
	                                    offset: cursor,
	                                    streamPath: options.streamPath ?? '',
	                                    message: 'CFB placement reference: incomplete counted display list or non-printable or incomplete reference tail',
	                                });
	                            }
	                        }
	                    }
	                    if (pendingPropertyPairsPayload) {
	                        const decodedPairs = OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.decodeTailPropertyIdPairs(pendingPropertyPairsPayload);
	                        if (record.role === 'global') {
	                            decodedPairs.pairs = (0, OrcadDsnCfbPagePropertyFrames_1.readPagePropertyFrame)(pendingPropertyPairsPayload, 0x25, reportPropertyFrame);
	                            if (decodedPairs.unrecognizedTag === 0x25) {
	                                if (decodedPairs.pairs === undefined) {
	                                    reportPropertyFrame('global-property-unframed');
	                                }
	                                decodedPairs.unrecognizedTag = undefined;
	                            }
	                        }
	                        if (decodedPairs.pairs) {
	                            record.propertyPairs = decodedPairs.pairs;
	                        }
	                        if (decodedPairs.unrecognizedTag !== undefined) {
	                            diagnostics.push({
	                                severity: 'warning',
	                                code: 'property-pair-block-unknown-tag',
	                                message: `Property row pair block carries tag 0x${decodedPairs.unrecognizedTag.toString(16)}, whose owning object kind is unknown, so its rows were not read`,
	                                specName: 'page.propertyPairs',
	                                streamPath: options.streamPath ?? '',
	                                details: { tag: decodedPairs.unrecognizedTag },
	                            });
	                        }
	                        pendingPropertyPairsPayload = undefined;
	                    }
	                    currentPlacedSymbol = record;
	                    currentDrawnInst = undefined;
	                    currentDrawnFrame = undefined;
	                }
	                if (record.kind === 'wire' && pendingPropertyPairsPayload) {
	                    record.propertyPairs = (0, OrcadDsnCfbPagePropertyFrames_1.readPagePropertyFrame)(pendingPropertyPairsPayload, 0x14, reportPropertyFrame);
	                }
	                if (record.kind === 'alias' && record.offset === payload.offset && aliasOwner !== undefined) {
	                    record.ownerWireDbId = aliasOwner;
	                }
	                if (record.kind === 'wire' &&
	                    record.offset === payload.offset &&
	                    payload.payloadByteLength === 52 &&
	                    payload.payload[31] === 0x31 &&
	                    payload.payload[40] === 0x31 &&
	                    payload.payload[49] === 0x31 &&
	                    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 41) === 11 &&
	                    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(payload.payload, 50) === 0xffff) {
	                    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(payload.payload, 29);
	                    if (count === 1) {
	                        pendingAliasOwner = record.dbId;
	                    }
	                    else {
	                        diagnostics.push({
	                            severity: 'warning',
	                            code: 'wire-alias-child-count-unsupported',
	                            streamPath: options.streamPath ?? '',
	                            offset: record.offset,
	                            message: `CFB alias [wire-alias-child-count-unsupported] wire=${record.dbId} count=${count}`,
	                        });
	                    }
	                }
	                if (record.kind === 'drawn-inst') {
	                    currentDrawnInst = record;
	                    const frames = drawnOwners.filter((frame) => frame.dataOffset <= record.offset && record.offset < frame.endOffset);
	                    currentDrawnFrame = frames.length === 1 ? frames[0] : undefined;
	                    if (currentDrawnFrame) {
	                        record.recordFrame = { offset: currentDrawnFrame.offset, endOffset: currentDrawnFrame.endOffset };
	                    }
	                    currentPlacedSymbol = undefined;
	                }
	                if ((record.kind === 'pin-to-wire' || record.kind === 'port-instance') && record.ownerName) {
	                    closesCurrentPlacedSymbol = true;
	                }
	                if (record.kind === 'drawn-port' && record.ownerName) {
	                    record.ownerReference = record.ownerName;
	                    if (currentDrawnInst) {
	                        currentDrawnInst.deviceDesignator = record.ownerName;
	                        currentDrawnInst.reference = record.ownerName;
	                    }
	                    closesCurrentDrawnInst = true;
	                }
	            }
	            if (closesCurrentPlacedSymbol) {
	                currentPlacedSymbol = undefined;
	            }
	            if (closesCurrentDrawnInst) {
	                currentDrawnInst = undefined;
	            }
	            pendingPropertyPairsPayload = payload.payload;
	        });
	        if (pendingAliasOwner !== undefined) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'wire-alias-child-unresolved',
	                streamPath: options.streamPath ?? '',
	                offset: bytes.length,
	                message: `CFB alias [wire-alias-child-unresolved] wire=${pendingAliasOwner}: child payload is absent`,
	            });
	        }
	        return {
	            records,
	            counts,
	            truncated,
	            decodedByteCount,
	            ...(shouldIncludeRawRecords(options) ? { decodedRecords } : {}),
	            diagnostics: shouldIncludeDiagnostics(options) ? diagnostics : (diagnostics.filter((diagnostic) => diagnostic.code.startsWith('typed-frame-') ||
	                diagnostic.code.startsWith('wire-alias-child-') ||
	                diagnostic.code === 'page-property-frame-withheld' ||
	                diagnostic.code === 'placement-reference-tail-withheld' ||
	                diagnostic.code === 'placement-value-withheld' ||
	                diagnostic.code === 'page-pin-frame-withheld')),
	        };
	    }
	    OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPageObjects = decodeOrCadDsnCfbPageObjects;
	    function decodeSymbolRecordSpecNode(spec, record, options = {}) {
	        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
	            bytes: record.payload,
	            offset: 0,
	            streamPath: options.streamPath ?? '',
	            streamName: options.streamName,
	            payloadOffset: record.offset,
	            payloadByteLength: record.payloadByteLength,
	            byteLength: record.byteLength,
	        });
	    }
	    function tailPinNumberMapRecordNode(bytes, offset, byteLength, options = {}, scanWork) {
	        const decoded = DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_TAIL_RECORD_SPECS[0], {
	            bytes,
	            offset,
	            streamPath: options.streamPath ?? '',
	            streamName: options.streamName,
	            payloadOffset: offset,
	            payloadByteLength: byteLength,
	            byteLength,
	            extra: { symbolScanWork: scanWork },
	        });
	        if (!decoded) {
	            return undefined;
	        }
	        const spanEnd = decoded.value.offset + decoded.value.byteLength;
	        const unknownRanges = (decoded.unknownRanges ?? []).flatMap((range) => {
	            const start = Math.max(range.offset, decoded.value.offset);
	            const end = Math.min(range.offset + range.byteLength, spanEnd);
	            return end > start ? [{ ...range, offset: start, byteLength: end - start }] : [];
	        });
	        const { unknownRanges: _budgetWideRanges, ...node } = decoded;
	        return {
	            ...node,
	            span: {
	                ...decoded.span,
	                offset: decoded.value.offset,
	                byteLength: decoded.value.byteLength,
	                payloadOffset: decoded.value.offset,
	                payloadByteLength: decoded.value.byteLength,
	            },
	            ...(unknownRanges.length > 0 ? { unknownRanges } : {}),
	        };
	    }
	    function isPlausiblePadMap(record) {
	        if (record.kind !== 'pin-number-map') {
	            return false;
	        }
	        return (record.entries.length > 0 &&
	            record.entries.every((entry, position) => entry.position === position) &&
	            !record.entries.some((entry) => SOURCE_LIBRARY_PATH_PATTERN.test(entry.number)));
	    }
	    function decodeSymbolObjectRecordNodes(record, options = {}, pinFrame, diagnostics) {
	        if (decodePrimitiveRecordNode(record.payload, 0)) {
	            return [];
	        }
	        const inlinePinNumberMap = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[0], record, options);
	        if (inlinePinNumberMap) {
	            return [inlinePinNumberMap];
	        }
	        const viewHeader = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[1], record, options);
	        const symbolPin = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[2], record, options);
	        const pin = symbolPin?.value.kind === 'symbol-pin' ? symbolPin.value : undefined;
	        const pinName = pin === undefined ? undefined : OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(record.payload, 0);
	        const pinBodyOffset = pinName === undefined ? undefined : OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pinName);
	        const hasPinFrame = pinFrame !== undefined &&
	            pinBodyOffset !== undefined &&
	            pinFrame.dataOffset === record.offset &&
	            record.offset + pinBodyOffset + 24 <= pinFrame.endOffset;
	        if (symbolPin && !hasPinFrame) {
	            diagnostics?.push({
	                severity: 'warning',
	                code: 'cache-pin-frame-withheld',
	                offset: record.offset,
	                streamPath: options.streamPath ?? '',
	                message: `CFB symbol pin [cache-pin-frame-withheld] offset=${record.offset}: no complete typed pin frame; pin withheld`,
	            });
	        }
	        if (viewHeader && !hasPinFrame) {
	            return [viewHeader];
	        }
	        if (symbolPin && hasPinFrame) {
	            return [symbolPin];
	        }
	        if (viewHeader) {
	            return [viewHeader];
	        }
	        const records = [];
	        const displayProperty = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[3], record, options);
	        if (displayProperty) {
	            records.push(displayProperty);
	        }
	        const partValue = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[4], record, options);
	        if (partValue) {
	            records.push(partValue);
	        }
	        if (records.length > 0) {
	            return records;
	        }
	        const namedRecord = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[5], record, options);
	        return namedRecord ? [namedRecord] : [];
	    }
	    function decodeOrCadDsnCfbSymbolObjects(bytes, options = {}) {
	        const diagnostics = [];
	        const typedPins = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x1a, 0x1b], 1, diagnostics, options.streamPath);
	        const maxRecords = options.maxRecords ?? Number.MAX_SAFE_INTEGER;
	        const records = [];
	        const decodedRecords = [];
	        const counts = createSymbolObjectCounts();
	        const scanWork = { candidateOffsets: 0, mapStringProbes: 0 };
	        let truncated = false;
	        let decodedByteCount = 0;
	        let tailOffset = bytes.length;
	        const isCacheRoleStream = DsnCfbBinarySpec_1.DsnCfbBinarySpec.matchStreamSpecs(OrcadDsnCfbStreamSpecs_1.ORCAD_DSN_CFB_STREAM_SPECS, {
	            path: options.streamPath ?? '',
	            name: options.streamName ?? '',
	            type: CFB_STREAM_ENTRY_TYPE,
	        }).some((spec) => spec.name === CACHE_STREAM_SPEC_NAME);
	        const consumedBytes = isCacheRoleStream ? new Uint8Array(bytes.length) : undefined;
	        const markConsumed = (start, byteLength) => {
	            if (!consumedBytes) {
	                return;
	            }
	            const end = Math.min(bytes.length, start + byteLength);
	            for (let i = Math.max(0, start); i < end; i++) {
	                consumedBytes[i] = 1;
	            }
	        };
	        const hasConsumedByte = (start, byteLength) => {
	            if (!consumedBytes) {
	                return false;
	            }
	            const end = Math.min(bytes.length, start + byteLength);
	            for (let i = Math.max(0, start); i < end; i++) {
	                if (consumedBytes[i]) {
	                    return true;
	                }
	            }
	            return false;
	        };
	        const pendingNamedRecords = [];
	        const addDecoded = (decodedNode) => {
	            if (isCacheRoleStream && decodedNode.value.kind === 'named-record') {
	                pendingNamedRecords.push(decodedNode);
	                return;
	            }
	            const decoded = decodedNode.value;
	            counts[decoded.kind]++;
	            decodedByteCount += decoded.byteLength;
	            markConsumed(decoded.offset, decoded.byteLength);
	            if (records.length < maxRecords) {
	                records.push(decoded);
	                if (shouldIncludeRawRecords(options)) {
	                    decodedRecords.push(decodedNode);
	                }
	            }
	            else {
	                truncated = true;
	            }
	            if (shouldIncludeDiagnostics(options)) {
	                diagnostics.push(...decodedNode.diagnostics);
	            }
	        };
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (payload) => {
	            if (payload.byteLength === payload.payloadByteLength) {
	                const tail = tailPinNumberMapRecordNode(bytes, payload.offset, payload.byteLength, options);
	                if (tail && isPlausiblePadMap(tail.value)) {
	                    tailOffset = payload.offset + payload.byteLength;
	                    addDecoded(tail);
	                    markConsumed(payload.offset, payload.byteLength);
	                    return;
	                }
	                tailOffset = payload.offset;
	            }
	            else {
	                tailOffset = payload.offset + payload.byteLength;
	            }
	            const decodedNodes = decodeSymbolObjectRecordNodes(payload, options, typedPins.get(payload.offset), diagnostics);
	            if (decodedNodes.length === 0) {
	                return;
	            }
	            tailOffset = payload.offset + payload.byteLength;
	            for (const decoded of decodedNodes) {
	                addDecoded(decoded);
	            }
	        });
	        if (isCacheRoleStream) {
	            let cursor = 0;
	            while (cursor < bytes.length) {
	                scanWork.candidateOffsets++;
	                if (hasConsumedByte(cursor, 1)) {
	                    cursor++;
	                    continue;
	                }
	                if (!OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength: 256 })) {
	                    cursor++;
	                    continue;
	                }
	                const found = tailPinNumberMapRecordNode(bytes, cursor, bytes.length - cursor, options, scanWork);
	                if (found && isPlausiblePadMap(found.value) && !hasConsumedByte(found.value.offset, found.value.byteLength)) {
	                    addDecoded(found);
	                    cursor = Math.max(cursor + 1, found.value.offset + found.value.byteLength);
	                    continue;
	                }
	                cursor++;
	            }
	            for (const pending of pendingNamedRecords) {
	                const decoded = pending.value;
	                if (hasConsumedByte(decoded.offset, decoded.byteLength)) {
	                    continue;
	                }
	                counts[decoded.kind]++;
	                decodedByteCount += decoded.byteLength;
	                markConsumed(decoded.offset, decoded.byteLength);
	                if (records.length < maxRecords) {
	                    records.push(decoded);
	                    if (shouldIncludeRawRecords(options)) {
	                        decodedRecords.push(pending);
	                    }
	                }
	                else {
	                    truncated = true;
	                }
	                if (shouldIncludeDiagnostics(options)) {
	                    diagnostics.push(...pending.diagnostics);
	                }
	            }
	        }
	        else {
	            const plausibleTailAt = (offset, searchByteLength) => {
	                const candidate = tailPinNumberMapRecordNode(bytes, offset, searchByteLength, options);
	                return candidate && isPlausiblePadMap(candidate.value) ? candidate : undefined;
	            };
	            const tailByteLength = bytes.length - tailOffset;
	            let tail = tailByteLength > 0 ? plausibleTailAt(tailOffset, tailByteLength) : undefined;
	            for (let offset = Math.max(tailOffset, bytes.length - 512); tailByteLength > 0 && !tail && offset < bytes.length; offset++) {
	                tail = plausibleTailAt(offset, bytes.length - offset);
	            }
	            if (tail) {
	                addDecoded(tail);
	            }
	        }
	        return {
	            records,
	            counts,
	            truncated,
	            decodedByteCount,
	            scanWork,
	            ...(shouldIncludeRawRecords(options) ? { decodedRecords } : {}),
	            diagnostics: shouldIncludeDiagnostics(options) ? diagnostics : (diagnostics.filter((diagnostic) => diagnostic.code === 'cache-pin-frame-withheld' || diagnostic.code.startsWith('typed-frame-'))),
	        };
	    }
	    OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbSymbolObjects = decodeOrCadDsnCfbSymbolObjects;
	})(OrcadDsnCfbRecordDecoders$1 || (OrcadDsnCfbRecordDecoders.OrcadDsnCfbRecordDecoders = OrcadDsnCfbRecordDecoders$1 = {}));
	
	return OrcadDsnCfbRecordDecoders;
}

var OrcadDsnCfbCacheSourceLibraries = {};

var OrcadDsnCfbCachePrimitiveFrames = {};

var hasRequiredOrcadDsnCfbCachePrimitiveFrames;

function requireOrcadDsnCfbCachePrimitiveFrames () {
	if (hasRequiredOrcadDsnCfbCachePrimitiveFrames) return OrcadDsnCfbCachePrimitiveFrames;
	hasRequiredOrcadDsnCfbCachePrimitiveFrames = 1;
	Object.defineProperty(OrcadDsnCfbCachePrimitiveFrames, "__esModule", { value: true });
	OrcadDsnCfbCachePrimitiveFrames.countCachePrimitiveFrames = countCachePrimitiveFrames;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	function countCachePrimitiveFrames(bytes, offset, record, records, primitives, diagnostics, extent) {
	    let cursor = offset;
	    let container = record;
	    let count = 0;
	    while (cursor < bytes.length) {
	        if (cursor === container.offset + container.byteLength) {
	            const next = records.get(cursor);
	            if (!next) {
	                return undefined;
	            }
	            container = next;
	        }
	        const end = container.offset + container.byteLength;
	        if (cursor + 2 > end) {
	            return undefined;
	        }
	        if (bytes[cursor] === 0 || bytes[cursor] !== bytes[cursor + 1]) {
	            if (extent) {
	                extent.endOffset = cursor;
	            }
	            return count;
	        }
	        if (cursor + 10 > end || container.byteLength === container.payloadByteLength) {
	            return undefined;
	        }
	        const byteLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, cursor + 2) + 10;
	        const compound = bytes[cursor] === 0x30 ? symbolVectorEndRecord(bytes, cursor, byteLength, container, records) : undefined;
	        if (byteLength <= 10 || (cursor + byteLength > end && !compound)) {
	            return undefined;
	        }
	        if (compound) {
	            container = compound;
	        }
	        const drawing = primitives.get(cursor);
	        if (!drawing || drawing.byteLength !== byteLength) {
	            const opcode = Array.from(bytes.subarray(cursor, cursor + 3), (value) => value.toString(16).padStart(2, '0')).join('');
	            diagnostics.push({
	                severity: 'warning',
	                code: 'cache-primitive-undecoded',
	                message: `CFB cache primitive [cache-primitive-undecoded] viewOffset=${record.offset} offset=${cursor} opcode=${opcode} byteLength=${byteLength}: frame counted; drawing withheld`,
	                streamPath: 'Root Entry/Cache',
	                offset: cursor,
	                details: { byteLength, viewOffset: record.offset },
	            });
	        }
	        count++;
	        cursor += byteLength;
	    }
	    if (extent) {
	        extent.endOffset = cursor;
	    }
	    return count;
	}
	function symbolVectorEndRecord(bytes, offset, byteLength, header, records) {
	    const end = offset + byteLength;
	    if (end > bytes.length ||
	        offset + 36 > end ||
	        header.offset + header.byteLength !== offset + 30 ||
	        bytes[offset + 10] !== 0x30 ||
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 11) !== 11 ||
	        bytes[offset + 19] !== 0x30 ||
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 20) !== 0xffff) {
	        return undefined;
	    }
	    let record = records.get(offset + 30);
	    if (!record) {
	        return undefined;
	    }
	    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 34);
	    let cursor = offset + 36;
	    for (let index = 0; index < count; index++) {
	        if (cursor + 11 > end || bytes[cursor + 1] !== 0 || bytes[cursor] !== bytes[cursor + 2]) {
	            return undefined;
	        }
	        const childEnd = cursor + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, cursor + 3) + 11;
	        if (childEnd > end ||
	            childEnd !== record.offset + record.byteLength ||
	            record.byteLength === record.payloadByteLength) {
	            return undefined;
	        }
	        const next = records.get(childEnd);
	        if (!next) {
	            return undefined;
	        }
	        record = next;
	        cursor = childEnd;
	    }
	    if (cursor + 3 > end || end > record.offset + record.payloadByteLength) {
	        return undefined;
	    }
	    const nameLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	    return cursor + nameLength + 3 === end && bytes[end - 1] === 0 ? record : undefined;
	}
	
	return OrcadDsnCfbCachePrimitiveFrames;
}

var hasRequiredOrcadDsnCfbCacheSourceLibraries;

function requireOrcadDsnCfbCacheSourceLibraries () {
	if (hasRequiredOrcadDsnCfbCacheSourceLibraries) return OrcadDsnCfbCacheSourceLibraries;
	hasRequiredOrcadDsnCfbCacheSourceLibraries = 1;
	Object.defineProperty(OrcadDsnCfbCacheSourceLibraries, "__esModule", { value: true });
	OrcadDsnCfbCacheSourceLibraries.decodeCacheSourceLibraries = decodeCacheSourceLibraries;
	const OrcadDsnCfbCachePrimitiveFrames_1 = requireOrcadDsnCfbCachePrimitiveFrames();
	const OrcadDsnCfbRecordDecoders_1 = requireOrcadDsnCfbRecordDecoders();
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	const OrcadDsnCfbTypedObjectFrames_1 = requireOrcadDsnCfbTypedObjectFrames();
	var CacheCatalogueRecordKind;
	(function (CacheCatalogueRecordKind) {
	    CacheCatalogueRecordKind["PhysicalHeader"] = "physical_header";
	})(CacheCatalogueRecordKind || (CacheCatalogueRecordKind = {}));
	function decodeCacheSourceLibraries(bytes, headers, diagnostics, primitives = OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords(bytes).records) {
	    const sources = [];
	    const pinFrames = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x1a, 0x1b], 1, diagnostics, 'Root Entry/Cache');
	    const headersByOffset = new Map(headers.map((header) => [header.offset, header]));
	    const primitivesByOffset = new Map(primitives.map((primitive) => [primitive.offset, primitive]));
	    const records = new Map();
	    const physicalHeaders = new Map();
	    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (record) => records.set(record.offset, record));
	    for (const record of records.values()) {
	        const header = headersByOffset.get(record.offset);
	        const end = Math.min(record.offset + record.payloadByteLength, header ? header.offset + header.payloadByteLength : bytes.length);
	        const name = readCountedLibraryField(bytes, record.offset, end);
	        const library = name ? readCountedLibraryField(bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name), end) : undefined;
	        const markerOffset = library ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(library) : undefined;
	        const viewMarker = markerOffset !== undefined &&
	            markerOffset + 6 <= end &&
	            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, markerOffset) === 0x30;
	        const terminated = record.byteLength > record.payloadByteLength &&
	            end === record.offset + record.payloadByteLength &&
	            (!header || record.offset + record.byteLength <= header.offset + header.byteLength);
	        const primitiveExtent = {};
	        const decodedPrimitiveCount = viewMarker && markerOffset !== undefined ?
	            (0, OrcadDsnCfbCachePrimitiveFrames_1.countCachePrimitiveFrames)(bytes, markerOffset + 6, record, records, primitivesByOffset, diagnostics, primitiveExtent)
	            : undefined;
	        const declaredPrimitiveCount = viewMarker && markerOffset !== undefined ?
	            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, markerOffset + 4)
	            : undefined;
	        const view = viewMarker && terminated && declaredPrimitiveCount === decodedPrimitiveCount;
	        if (view && header?.kind === 'view-header' && primitiveExtent.endOffset !== undefined) {
	            const tableOffset = primitiveExtent.endOffset;
	            const table = records.get(tableOffset);
	            if (table &&
	                table.payloadByteLength >= 22 &&
	                table.byteLength > table.payloadByteLength &&
	                OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, tableOffset) === 0 &&
	                pinFrames.get(tableOffset + table.byteLength)?.offset === tableOffset + 10) {
	                const fields = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
	                const width = fields.getInt16(tableOffset + 4, true);
	                const height = fields.getInt16(tableOffset + 6, true);
	                if (width < 0 || height < 0) {
	                    delete header.declaredBoundingBox;
	                    diagnostics.push({
	                        severity: 'warning',
	                        code: 'cache-view-bounds-withheld',
	                        offset: tableOffset,
	                        message: `CFB view bounds: unsupported signed extent width=${width} height=${height}; declared box withheld`,
	                    });
	                }
	                else {
	                    header.declaredBoundingBox = { X1: 0, Y1: 0, X2: width, Y2: height };
	                }
	            }
	        }
	        const catalogue = markerOffset !== undefined && terminated ? readViewCatalogueBody(bytes, markerOffset, end) : undefined;
	        if (catalogue?.physicalHeader) {
	            const bodyOffset = record.offset + record.byteLength;
	            physicalHeaders.set(bodyOffset, {
	                kind: CacheCatalogueRecordKind.PhysicalHeader,
	                bodyOffset,
	                ...catalogue.physicalHeader,
	            });
	        }
	        const physicalHeader = physicalHeaders.get(record.offset);
	        const physical = (markerOffset !== undefined &&
	            terminated &&
	            physicalHeader?.kind === CacheCatalogueRecordKind.PhysicalHeader &&
	            physicalHeader.name.value === name?.value &&
	            physicalHeader.library.value === library?.value) ?
	            readPhysicalCatalogueBody(bytes, markerOffset, end, record.offset + record.byteLength, records, physicalHeader)
	            : undefined;
	        const physicalCountAgrees = physical !== undefined && physical.declaredSectionCount === physical.decodedSectionCount;
	        if (name && library && (view || catalogue || physicalCountAgrees)) {
	            sources.push({
	                headerOffset: record.offset,
	                library,
	                ...(physicalCountAgrees ? { physicalPackageName: name.value, physicalSections: physical.sections } : {}),
	                ...(catalogue?.continuationLibrary ? { continuationLibrary: catalogue.continuationLibrary } : {}),
	            });
	            if (physicalCountAgrees && physical?.nextHeader) {
	                physicalHeaders.set(physical.nextHeader.bodyOffset, physical.nextHeader);
	            }
	        }
	        else if (header?.kind === 'view-header' || library?.value) {
	            const code = viewMarker && !terminated ? 'cache-source-library-unterminated'
	                : viewMarker && declaredPrimitiveCount !== decodedPrimitiveCount ?
	                    'cache-source-library-primitive-count-mismatch'
	                    : physical && !physicalCountAgrees ? 'cache-source-library-section-count-mismatch'
	                        : (physicalHeader &&
	                            (physicalHeader.name.value !== name?.value || physicalHeader.library.value !== library?.value)) ?
	                            'cache-source-library-physical-header-mismatch'
	                            : 'cache-source-library-unframed';
	            diagnostics.push({
	                severity: 'warning',
	                code,
	                message: `CFB cache source library [${code}] header=${JSON.stringify(name?.value)} offset=${record.offset}${viewMarker ? ` declared=${declaredPrimitiveCount} decoded=${decodedPrimitiveCount}`
	                    : physical ?
	                        ` declared=${physical.declaredSectionCount} decoded=${physical.decodedSectionCount ?? 'unframed'}`
	                        : ''}`,
	                streamPath: 'Root Entry/Cache',
	                offset: record.offset,
	            });
	        }
	    }
	    return sources;
	}
	function readPhysicalCatalogueBody(bytes, offset, end, sectionOffset, records, header) {
	    const reference = readCountedLibraryField(bytes, offset, end);
	    const footprint = reference ?
	        readCountedLibraryField(bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(reference) + 3, end)
	        : undefined;
	    if (!footprint) {
	        return undefined;
	    }
	    const countOffset = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(footprint);
	    if (countOffset + 2 > end || !readTaggedTail(bytes, countOffset + 2, end, 0x20, 0xffff)) {
	        return undefined;
	    }
	    const sections = readPhysicalSectionCount(bytes, sectionOffset, records, header.name);
	    return {
	        declaredSectionCount: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, countOffset),
	        decodedSectionCount: sections?.count,
	        sections: sections?.sections,
	        ...(sections?.nextHeader ? { nextHeader: sections.nextHeader } : {}),
	    };
	}
	function readPhysicalSectionCount(bytes, offset, records, name) {
	    let cursor = offset;
	    let count = 0;
	    const sections = [];
	    while (cursor < bytes.length) {
	        const record = records.get(cursor);
	        if (!record) {
	            return undefined;
	        }
	        const end = record.offset + record.payloadByteLength;
	        const selector = readCountedLibraryField(bytes, cursor, end);
	        const cell = selector ?
	            readCountedLibraryField(bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(selector), end)
	            : undefined;
	        if (!cell?.value) {
	            return undefined;
	        }
	        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(cell);
	        if (cursor + 2 > end) {
	            return undefined;
	        }
	        const pinCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	        cursor += 2;
	        const pinNumbers = [];
	        for (let pinIndex = 0; pinIndex < pinCount; pinIndex++) {
	            const pin = readCountedLibraryField(bytes, cursor, end);
	            if (!pin) {
	                return undefined;
	            }
	            pinNumbers.push(pin.value);
	            cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pin) + 1;
	            if (cursor > end) {
	                return undefined;
	            }
	        }
	        count++;
	        sections.push({ offset: record.offset, selector: selector?.value ?? '', cellName: cell.value, pinNumbers });
	        if (end === bytes.length) {
	            return readEmptyCatalogueEntries(bytes, cursor, end) ? { count, sections } : undefined;
	        }
	        if (record.byteLength === record.payloadByteLength) {
	            return undefined;
	        }
	        if (readTaggedTail(bytes, cursor, end, 0x20, 0xffff)) {
	            cursor = record.offset + record.byteLength;
	            continue;
	        }
	        const library = readCatalogueLibraryPreamble(bytes, cursor, end, 0x1f, 0);
	        const named = readCataloguePreamble(bytes, cursor, end, false, 0x1f, 0);
	        if (Boolean(library) === Boolean(named)) {
	            return undefined;
	        }
	        const reference = named ?? (library ? { name, library } : undefined);
	        return reference ?
	            {
	                count,
	                sections,
	                nextHeader: {
	                    kind: CacheCatalogueRecordKind.PhysicalHeader,
	                    bodyOffset: record.offset + record.byteLength,
	                    ...reference,
	                },
	            }
	            : undefined;
	    }
	    return undefined;
	}
	function readEmptyCatalogueEntries(bytes, offset, end) {
	    let cursor = offset;
	    while (cursor < end) {
	        const name = readCountedLibraryField(bytes, cursor, end);
	        if (!name?.value) {
	            return false;
	        }
	        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name);
	        if (cursor + 2 > end || OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor) !== 0) {
	            return false;
	        }
	        cursor += 2;
	    }
	    return cursor === end;
	}
	function readViewCatalogueBody(bytes, offset, end) {
	    if (offset + 2 > end) {
	        return undefined;
	    }
	    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	    let cursor = offset + 2;
	    for (let index = 0; index < count; index++) {
	        const view = readCountedLibraryField(bytes, cursor, end);
	        if (!view) {
	            return undefined;
	        }
	        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(view);
	    }
	    const continuation = readCataloguePreamble(bytes, cursor, end, false);
	    const libraryContinuation = readCatalogueLibraryPreamble(bytes, cursor, end, 0x06, 0xffff);
	    let tableCursor = cursor;
	    while (tableCursor < end) {
	        const name = readCountedLibraryField(bytes, tableCursor, end);
	        const countOffset = name ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name) : end;
	        if (countOffset + 2 > end || OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, countOffset) !== 0) {
	            break;
	        }
	        tableCursor = countOffset + 2;
	    }
	    const tableBoundary = readCataloguePreamble(bytes, tableCursor, end, true);
	    if ([continuation, tableBoundary, libraryContinuation].filter(Boolean).length !== 1) {
	        return undefined;
	    }
	    return {
	        ...(libraryContinuation ? { continuationLibrary: libraryContinuation } : {}),
	        ...(tableBoundary ? { physicalHeader: tableBoundary } : {}),
	    };
	}
	function readCataloguePreamble(bytes, offset, end, tableBoundary, tag = tableBoundary ? 0x1f : 0x06, closingWord = tableBoundary ? 0 : 0xffff) {
	    let cursor = offset + (tableBoundary ? 2 : 0);
	    while (cursor < end) {
	        const name = readCountedLibraryField(bytes, cursor, end);
	        if (!name) {
	            return undefined;
	        }
	        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name);
	        if (cursor + 2 > end) {
	            return undefined;
	        }
	        const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	        cursor += 2;
	        if (count > 0) {
	            const library = readCatalogueLibraryPreamble(bytes, cursor, end, tag, closingWord);
	            return library ? { name, library } : undefined;
	        }
	    }
	    return undefined;
	}
	function readCatalogueLibraryPreamble(bytes, offset, end, tag, closingWord) {
	    const library = readCountedLibraryField(bytes, offset, end);
	    if (!library) {
	        return undefined;
	    }
	    const cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(library) + 8;
	    return (cursor + 2 <= end &&
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor) === tag &&
	        readTaggedTail(bytes, cursor + 2, end, tag, closingWord)) ?
	        library
	        : undefined;
	}
	function readTaggedTail(bytes, offset, end, tag, closingWord) {
	    let cursor = offset;
	    while (cursor + 3 < end) {
	        if (cursor + 9 > end || bytes[cursor] !== tag) {
	            return false;
	        }
	        cursor += 9;
	    }
	    return (cursor + 3 === end &&
	        bytes[cursor] === tag &&
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 1) === closingWord);
	}
	function readCountedLibraryField(bytes, offset, end) {
	    if (offset < 0 || offset + 2 > end || end > bytes.length) {
	        return undefined;
	    }
	    const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	    const terminator = offset + 2 + length;
	    if (terminator >= end || bytes[terminator] !== 0) {
	        return undefined;
	    }
	    const valueBytes = bytes.subarray(offset + 2, terminator);
	    if (valueBytes.some((byte) => byte < 0x20)) {
	        return undefined;
	    }
	    return {
	        offset,
	        length,
	        hasNullTerminator: true,
	        value: Array.from(valueBytes, (byte) => String.fromCharCode(byte)).join(''),
	    };
	}
	
	return OrcadDsnCfbCacheSourceLibraries;
}

var OrcadDsnCfbHierarchyPhysicalNames = {};

var hasRequiredOrcadDsnCfbHierarchyPhysicalNames;

function requireOrcadDsnCfbHierarchyPhysicalNames () {
	if (hasRequiredOrcadDsnCfbHierarchyPhysicalNames) return OrcadDsnCfbHierarchyPhysicalNames;
	hasRequiredOrcadDsnCfbHierarchyPhysicalNames = 1;
	Object.defineProperty(OrcadDsnCfbHierarchyPhysicalNames, "__esModule", { value: true });
	OrcadDsnCfbHierarchyPhysicalNames.readHierarchyPhysicalNetNames = readHierarchyPhysicalNetNames;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	function readHierarchyPhysicalNetNames(bytes, viewName, streamPath, diagnostics) {
	    const records = [];
	    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (record) => records.push(record));
	    const fail = (reason, offset) => {
	        diagnostics.push({
	            severity: 'warning',
	            code: 'hierarchy-physical-name-frame-withheld',
	            streamPath,
	            offset,
	            message: `CFB physical net name [hierarchy-physical-name-frame-withheld] view=${JSON.stringify(viewName)} reason=${reason} offset=${offset}`,
	        });
	        return [];
	    };
	    const header = records[0];
	    if (!header || header.offset !== 0 || bytes[0] !== 0x42 || header.byteLength === header.payloadByteLength) {
	        return fail('root-header-unframed', 0);
	    }
	    const name = countedName(bytes, header, 9);
	    if (!name || name.value.toUpperCase() !== viewName.toUpperCase()) {
	        return fail('root-view-mismatch', 9);
	    }
	    let record = header;
	    let cursor = name.after + 7;
	    let index = 1;
	    if (cursor + 2 > record.offset + record.payloadByteLength) {
	        return fail('port-count-truncated', cursor);
	    }
	    const portCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	    cursor += 2;
	    for (let port = 0; port < portCount; port++) {
	        if (!tableFrame(bytes, record, cursor, 0x44)) {
	            return fail('port-frame-unresolved', cursor);
	        }
	        const next = records[index++];
	        if (!next) {
	            return fail('port-record-missing', cursor);
	        }
	        record = next;
	        const field = countedName(bytes, record, record.offset + 4);
	        if (!field) {
	            return fail('port-name-truncated', record.offset);
	        }
	        cursor = field.after;
	    }
	    if (cursor + 2 > record.offset + record.payloadByteLength) {
	        return fail('net-count-truncated', cursor);
	    }
	    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	    cursor += 2;
	    const names = [];
	    for (let net = 0; net < count; net++) {
	        if (!tableFrame(bytes, record, cursor, 0x43)) {
	            return fail('net-frame-unresolved', cursor);
	        }
	        const next = records[index++];
	        if (!next || next.byteLength === next.payloadByteLength) {
	            return fail('net-record-unclosed', cursor);
	        }
	        record = next;
	        const field = countedName(bytes, record, record.offset + 4);
	        if (!field) {
	            return fail('net-name-truncated', record.offset);
	        }
	        names.push(field.value);
	        cursor = field.after;
	    }
	    if (tableFrame(bytes, record, cursor, 0x43)) {
	        return fail('net-count-mismatch', cursor);
	    }
	    return names;
	}
	function countedName(bytes, record, offset) {
	    const end = record.offset + record.payloadByteLength;
	    if (offset + 2 > end) {
	        return undefined;
	    }
	    const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	    const after = offset + 2 + length;
	    if (after >= end || bytes[after] !== 0) {
	        return undefined;
	    }
	    const valueBytes = bytes.subarray(offset + 2, after);
	    if (valueBytes.some((byte) => byte < 0x20)) {
	        return undefined;
	    }
	    return { value: Array.from(valueBytes, (byte) => String.fromCharCode(byte)).join(''), after: after + 1 };
	}
	function tableFrame(bytes, record, offset, kind) {
	    const end = record.offset + record.payloadByteLength;
	    if (record.byteLength === record.payloadByteLength ||
	        offset + 12 > end ||
	        bytes[offset] !== kind ||
	        bytes[offset + 9] !== kind) {
	        return false;
	    }
	    const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
	    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 10);
	    return length === 11 + 8 * count && offset + length + 1 === end;
	}
	
	return OrcadDsnCfbHierarchyPhysicalNames;
}

var OrcadDsnCfbOccurrenceFrames = {};

var hasRequiredOrcadDsnCfbOccurrenceFrames;

function requireOrcadDsnCfbOccurrenceFrames () {
	if (hasRequiredOrcadDsnCfbOccurrenceFrames) return OrcadDsnCfbOccurrenceFrames;
	hasRequiredOrcadDsnCfbOccurrenceFrames = 1;
	Object.defineProperty(OrcadDsnCfbOccurrenceFrames, "__esModule", { value: true });
	OrcadDsnCfbOccurrenceFrames.readHierarchyOccurrenceFrame = readHierarchyOccurrenceFrame;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	function readHierarchyOccurrenceFrame(bytes, record, records, closedBranch = false) {
	    const kindOffset = record.offset + 8;
	    if (record.payloadByteLength < 20 || bytes[kindOffset] !== 0x42) {
	        return undefined;
	    }
	    const bodyOffset = kindOffset + 11;
	    const bodyEnd = bodyOffset + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, kindOffset + 1);
	    const closedPayload = (payload) => payload.byteLength !== payload.payloadByteLength ||
	        (closedBranch && bodyEnd === bytes.length && payload.offset + payload.payloadByteLength === bodyEnd);
	    let container = record;
	    let cursor = bodyOffset + 1;
	    const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	    const nameEnd = cursor + 2 + length;
	    if (nameEnd + 7 > container.offset + container.payloadByteLength || bytes[nameEnd] !== 0 || bodyEnd > bytes.length) {
	        return undefined;
	    }
	    const name = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, cursor + 2, nameEnd);
	    cursor = nameEnd + 1;
	    const selectorIndex = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, cursor);
	    cursor += 4;
	    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
	    cursor += 2;
	    for (let index = 0; index < count; index++) {
	        const end = container.offset + container.payloadByteLength;
	        if (!closedPayload(container) || cursor + 12 > end || bytes[cursor] !== 0x44 || bytes[cursor + 9] !== 0x44) {
	            return undefined;
	        }
	        const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 10);
	        const propertyLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, cursor + 1);
	        if (propertyLength !== 11 + propertyCount * 8 || cursor + propertyLength + 1 !== end) {
	            return undefined;
	        }
	        const next = records.get(container.offset + container.byteLength);
	        if (!next || next.payloadByteLength < 6 || !closedPayload(next)) {
	            return undefined;
	        }
	        container = next;
	        cursor = container.offset + 6;
	    }
	    if (cursor + 10 !== bodyEnd ||
	        bodyEnd > container.offset + container.payloadByteLength ||
	        !closedPayload(container)) {
	        return undefined;
	    }
	    if (bytes.subarray(cursor, bodyEnd).some((byte) => byte !== 0)) {
	        return undefined;
	    }
	    return { name, nameOffset: bodyOffset + 1, selectorIndex };
	}
	
	return OrcadDsnCfbOccurrenceFrames;
}

var OrcadDsnCfbHierarchyScopes = {};

var OrcadDsnCfbHierarchyViewFrames = {};

var hasRequiredOrcadDsnCfbHierarchyViewFrames;

function requireOrcadDsnCfbHierarchyViewFrames () {
	if (hasRequiredOrcadDsnCfbHierarchyViewFrames) return OrcadDsnCfbHierarchyViewFrames;
	hasRequiredOrcadDsnCfbHierarchyViewFrames = 1;
	Object.defineProperty(OrcadDsnCfbHierarchyViewFrames, "__esModule", { value: true });
	OrcadDsnCfbHierarchyViewFrames.readHierarchyViewBody = readHierarchyViewBody;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	function mergeCursors(states) {
	    const merged = new Map();
	    for (const state of states) {
	        const previous = merged.get(state.offset);
	        if (previous === undefined) {
	            merged.set(state.offset, state);
	        }
	        else if (previous.rows !== state.rows || previous.record !== state.record) {
	            return undefined;
	        }
	    }
	    return [...merged.values()];
	}
	function readHierarchyViewBody(bytes, kindOffset, records, diagnostics, streamPath) {
	    const fail = (reason) => {
	        diagnostics.push({
	            severity: 'warning',
	            code: 'hierarchy-ownership-body-frame-withheld',
	            streamPath,
	            offset: kindOffset,
	            message: `CFB hierarchy ownership: ${reason}; complete branch body withheld`,
	        });
	        return undefined;
	    };
	    const start = kindOffset === 0 ? 0 : kindOffset - 8;
	    const initial = records.findUniqueContainingPayload(start);
	    if (!initial || kindOffset + 11 > initial.offset + initial.payloadByteLength || bytes[kindOffset] !== 0x42) {
	        return fail('named header is not framed');
	    }
	    const nameLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, kindOffset + 9);
	    const nameEnd = kindOffset + 11 + nameLength;
	    if (nameLength === 0 || nameEnd + 8 > initial.offset + initial.payloadByteLength || bytes[nameEnd] !== 0) {
	        return fail('counted branch name crosses its payload');
	    }
	    const nameBytes = bytes.subarray(kindOffset + 11, nameEnd);
	    const name = nameBytes.some((byte) => byte < 0x20 || byte > 0x7e) ? undefined : (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, kindOffset + 11, nameEnd));
	    const metadataLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, nameEnd + 1);
	    const metadataEnd = nameEnd + 3 + metadataLength;
	    if (metadataEnd + 5 > initial.offset + initial.payloadByteLength || bytes[metadataEnd] !== 0) {
	        return fail('counted branch metadata crosses its payload');
	    }
	    let states = [{ record: initial, offset: metadataEnd + 5 }];
	    const tables = [
	        { kinds: [0x44, 0x45], countBytes: 2, identifiersOnly: false, allowKey: true },
	        { kinds: [0x43], countBytes: 2, identifiersOnly: false, allowKey: false },
	        { kinds: [0x52], countBytes: 2, identifiersOnly: true, allowKey: false },
	        { kinds: [0x5b], countBytes: 4, identifiersOnly: true, allowKey: false },
	    ];
	    for (const table of tables) {
	        const completed = [];
	        for (const state of states) {
	            if (state.offset + table.countBytes > state.record.offset + state.record.payloadByteLength) {
	                continue;
	            }
	            const count = table.countBytes === 2 ?
	                OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, state.offset)
	                : OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, state.offset);
	            let candidates = [{ ...state, offset: state.offset + table.countBytes }];
	            for (let index = 0; index < count && candidates.length > 0; index++) {
	                const nextCandidates = [];
	                for (const candidate of candidates) {
	                    const offset = candidate.offset;
	                    const recordEnd = candidate.record.offset + candidate.record.payloadByteLength;
	                    const kind = bytes[offset];
	                    if (offset + 12 > recordEnd ||
	                        !table.kinds.some((allowed) => allowed === kind) ||
	                        bytes[offset + 9] !== kind ||
	                        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5) !== 0) {
	                        continue;
	                    }
	                    const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 10);
	                    const propertyLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
	                    if (propertyLength !== 11 + propertyCount * 8 ||
	                        offset + 1 + propertyLength !== recordEnd ||
	                        candidate.record.byteLength !== candidate.record.payloadByteLength + 8) {
	                        continue;
	                    }
	                    const record = records.get(candidate.record.offset + candidate.record.byteLength);
	                    if (!record || record.payloadByteLength < 4) {
	                        continue;
	                    }
	                    const row = {
	                        kind,
	                        frameOffset: offset,
	                        offset: record.offset,
	                        occurrenceId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, record.offset),
	                    };
	                    const end = record.offset + record.payloadByteLength;
	                    if (table.identifiersOnly) {
	                        if (record.offset + 8 <= end) {
	                            nextCandidates.push({
	                                record,
	                                offset: record.offset + 8,
	                                rows: {
	                                    row: { ...row, referenceId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, record.offset + 4) },
	                                    previous: candidate.rows,
	                                },
	                            });
	                        }
	                        continue;
	                    }
	                    if (record.offset + 6 > end) {
	                        continue;
	                    }
	                    const field = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, record.offset + 4);
	                    if (table.allowKey) {
	                        nextCandidates.push({
	                            record,
	                            offset: record.offset + 6,
	                            rows: { row: { ...row, key: field }, previous: candidate.rows },
	                        });
	                    }
	                    const afterName = record.offset + 6 + field;
	                    if (afterName < end &&
	                        bytes[afterName] === 0 &&
	                        !bytes.subarray(record.offset + 6, afterName).some((byte) => byte < 0x20 || byte > 0x7e)) {
	                        nextCandidates.push({
	                            record,
	                            offset: afterName + 1,
	                            rows: {
	                                row: { ...row, name: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, record.offset + 6, afterName) },
	                                previous: candidate.rows,
	                            },
	                        });
	                    }
	                }
	                const mergedCandidates = mergeCursors(nextCandidates);
	                if (mergedCandidates === undefined) {
	                    return fail('counted table framing is ambiguous');
	                }
	                candidates = mergedCandidates;
	            }
	            completed.push(...candidates);
	        }
	        const mergedStates = mergeCursors(completed);
	        if (mergedStates === undefined) {
	            return fail('counted table framing is ambiguous');
	        }
	        states = mergedStates;
	    }
	    states = states.filter((state) => {
	        const end = bodyEnd(bytes, state);
	        return end !== undefined && hasChildListHeader(bytes, end);
	    });
	    if (states.length !== 1) {
	        return fail(states.length === 0 ? 'counted table framing is incomplete' : 'counted table framing is ambiguous');
	    }
	    const state = states[0];
	    const endOffset = bodyEnd(bytes, state);
	    if (endOffset === undefined) {
	        return fail('body continuation is incomplete');
	    }
	    const rows = [];
	    for (let link = state.rows; link !== undefined; link = link.previous) {
	        rows.push(link.row);
	    }
	    return {
	        name,
	        endOffset,
	        rows: rows.reverse(),
	    };
	}
	function bodyEnd(bytes, state) {
	    if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, state.offset, [0xff, 0xe4, 0x5c, 0x39])) {
	        if (state.offset + 8 > bytes.length) {
	            return undefined;
	        }
	        const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, state.offset + 4);
	        const end = state.offset + 8 + length;
	        const payloadEnd = state.record.offset + state.record.payloadByteLength;
	        const closesRecord = length === 0 && state.offset === payloadEnd && end === state.record.offset + state.record.byteLength;
	        return end <= payloadEnd || closesRecord ? end : undefined;
	    }
	    return state.offset < state.record.offset + state.record.payloadByteLength ? state.offset : undefined;
	}
	function hasChildListHeader(bytes, offset) {
	    if (offset + 2 > bytes.length) {
	        return false;
	    }
	    if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset) === 0) {
	        return true;
	    }
	    const base = offset + 2;
	    if (base + 20 > bytes.length ||
	        bytes[base] !== 0x42 ||
	        bytes[base + 9] !== 0x42 ||
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, base + 5) !== 0) {
	        return false;
	    }
	    const end = base + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, base + 1);
	    const terminator = base + 12 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, base + 10) * 8;
	    return (end + 19 <= bytes.length &&
	        terminator + 8 <= end &&
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator, [0xff, 0xe4, 0x5c, 0x39]) &&
	        terminator + 8 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, terminator + 4) === end &&
	        bytes[end + 8] === 0x42);
	}
	
	return OrcadDsnCfbHierarchyViewFrames;
}

var OrcadDsnCfbPayloadIndex = {};

var hasRequiredOrcadDsnCfbPayloadIndex;

function requireOrcadDsnCfbPayloadIndex () {
	if (hasRequiredOrcadDsnCfbPayloadIndex) return OrcadDsnCfbPayloadIndex;
	hasRequiredOrcadDsnCfbPayloadIndex = 1;
	Object.defineProperty(OrcadDsnCfbPayloadIndex, "__esModule", { value: true });
	OrcadDsnCfbPayloadIndex.OrcadDsnCfbPayloadIndex = void 0;
	function buildSpans(records) {
	    const events = new Map();
	    for (const record of records) {
	        if (record.payloadByteLength <= 0) {
	            continue;
	        }
	        const end = record.offset + record.payloadByteLength;
	        const opening = events.get(record.offset) ?? { starts: [], ends: [] };
	        opening.starts.push(record);
	        events.set(record.offset, opening);
	        const closing = events.get(end) ?? { starts: [], ends: [] };
	        closing.ends.push(record);
	        events.set(end, closing);
	    }
	    const points = [...events].sort(([left], [right]) => left - right);
	    const active = new Set();
	    const spans = [];
	    for (let index = 0; index + 1 < points.length; index++) {
	        const [start, event] = points[index];
	        for (const record of event.ends) {
	            active.delete(record);
	        }
	        for (const record of event.starts) {
	            active.add(record);
	        }
	        spans.push({
	            start,
	            end: points[index + 1][0],
	            record: active.size === 1 ? [...active][0] : undefined,
	        });
	    }
	    return spans;
	}
	class OrcadDsnCfbPayloadIndex$1 {
	    constructor(records) {
	        this.records = new Map();
	        const snapshot = records.map((record) => Object.freeze({ ...record }));
	        const duplicateOffsets = new Set();
	        for (const record of snapshot) {
	            if (this.records.has(record.offset)) {
	                duplicateOffsets.add(record.offset);
	            }
	            else {
	                this.records.set(record.offset, record);
	            }
	        }
	        for (const offset of duplicateOffsets) {
	            this.records.delete(offset);
	        }
	        this.spans = buildSpans(snapshot);
	    }
	    get(offset) {
	        return this.records.get(offset);
	    }
	    findUniqueContainingPayload(offset) {
	        let low = 0;
	        let high = this.spans.length;
	        while (low < high) {
	            const middle = Math.floor((low + high) / 2);
	            if (this.spans[middle].start <= offset) {
	                low = middle + 1;
	            }
	            else {
	                high = middle;
	            }
	        }
	        const span = this.spans[low - 1];
	        return span !== undefined && offset < span.end ? span.record : undefined;
	    }
	}
	OrcadDsnCfbPayloadIndex.OrcadDsnCfbPayloadIndex = OrcadDsnCfbPayloadIndex$1;
	
	return OrcadDsnCfbPayloadIndex;
}

var hasRequiredOrcadDsnCfbHierarchyScopes;

function requireOrcadDsnCfbHierarchyScopes () {
	if (hasRequiredOrcadDsnCfbHierarchyScopes) return OrcadDsnCfbHierarchyScopes;
	hasRequiredOrcadDsnCfbHierarchyScopes = 1;
	Object.defineProperty(OrcadDsnCfbHierarchyScopes, "__esModule", { value: true });
	OrcadDsnCfbHierarchyScopes.readHierarchyOccurrenceScopes = readHierarchyOccurrenceScopes;
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
	const OrcadDsnCfbHierarchyViewFrames_1 = requireOrcadDsnCfbHierarchyViewFrames();
	const OrcadDsnCfbPayloadIndex_1 = requireOrcadDsnCfbPayloadIndex();
	const MAX_HIERARCHY_DEPTH = 256;
	function readHierarchyOccurrenceScopes(bytes, rootView, diagnostics, streamPath, branches = [], parents = new Map()) {
	    const scopes = new Map();
	    const namedPaths = new Map();
	    const records = new Map();
	    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (record) => records.set(record.offset, record));
	    const payloadIndex = new OrcadDsnCfbPayloadIndex_1.OrcadDsnCfbPayloadIndex([...records.values()]);
	    const propertyEnd = (offset) => {
	        if (offset + 20 > bytes.length ||
	            bytes[offset] !== 0x42 ||
	            bytes[offset + 9] !== 0x42 ||
	            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5) !== 0) {
	            return undefined;
	        }
	        const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 10);
	        const end = offset + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
	        const terminator = offset + 12 + count * 8;
	        if (end > bytes.length ||
	            terminator + 8 > end ||
	            !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator, [0xff, 0xe4, 0x5c, 0x39]) ||
	            terminator + 8 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, terminator + 4) !== end) {
	            return undefined;
	        }
	        return end;
	    };
	    const openBranch = (kindOffset, parent) => {
	        const frame = (0, OrcadDsnCfbHierarchyViewFrames_1.readHierarchyViewBody)(bytes, kindOffset, payloadIndex, diagnostics, streamPath);
	        if (!frame || frame.endOffset + 2 > bytes.length) {
	            const nameEnd = kindOffset + 11 + (kindOffset + 11 <= bytes.length ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, kindOffset + 9) : 0);
	            const nameRecord = payloadIndex.findUniqueContainingPayload(kindOffset);
	            const name = (bytes[kindOffset] === 0x42 &&
	                nameEnd > kindOffset + 11 &&
	                nameRecord !== undefined &&
	                nameEnd < nameRecord.offset + nameRecord.payloadByteLength &&
	                bytes[nameEnd] === 0 &&
	                !bytes.subarray(kindOffset + 11, nameEnd).some((byte) => byte < 0x20 || byte > 0x7e)) ?
	                OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, kindOffset + 11, nameEnd)
	                : undefined;
	            branches.push({
	                offset: kindOffset,
	                viewName: name,
	                state: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Unframed,
	            });
	            return undefined;
	        }
	        const depth = parent === undefined ? 0 : parent.depth + 1;
	        const withinBudget = depth < MAX_HIERARCHY_DEPTH;
	        const offsets = withinBudget ? [...(parent?.offsets ?? []), kindOffset] : [];
	        const reservedWord = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, kindOffset + 5);
	        const declaredEnd = kindOffset + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, kindOffset + 1);
	        const name = frame.name;
	        const rootMatches = kindOffset !== 0 || name === undefined || name.toUpperCase() === rootView.toUpperCase();
	        if (declaredEnd !== frame.endOffset) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-ownership-body-length-invalid',
	                streamPath,
	                offset: kindOffset + 1,
	                message: `CFB hierarchy ownership: declared view boundary ${declaredEnd} disagrees with complete table boundary ${frame.endOffset}; branch ownership withheld`,
	            });
	        }
	        if (reservedWord !== 0) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-ownership-reserved-word-invalid',
	                streamPath,
	                offset: kindOffset + 5,
	                message: `CFB hierarchy ownership: view ${JSON.stringify(name) ?? '<undecodable>'}: reserved word at header +5 is ${reservedWord}; branch ownership withheld`,
	            });
	        }
	        if (name === undefined) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-ownership-name-withheld',
	                streamPath,
	                offset: kindOffset + 9,
	                message: 'CFB hierarchy ownership: branch name is not a complete printable counted string; its occurrence rows remain unbound',
	            });
	        }
	        if (!withinBudget) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-ownership-depth-withheld',
	                streamPath,
	                offset: kindOffset,
	                message: `CFB hierarchy ownership: ownership resource budget of ${MAX_HIERARCHY_DEPTH} nested branches exceeded; this branch's ownership withheld`,
	            });
	        }
	        if (!rootMatches) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-ownership-root-name-mismatch',
	                streamPath,
	                offset: kindOffset,
	                message: 'CFB hierarchy ownership: recorded root view disagrees with its storage identity; root ownership withheld',
	            });
	        }
	        return {
	            kindOffset,
	            name,
	            depth,
	            offsets,
	            cursor: frame.endOffset + 2,
	            remaining: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, frame.endOffset),
	            complete: true,
	            valid: declaredEnd === frame.endOffset && reservedWord === 0 && withinBudget && rootMatches,
	            leaves: [],
	            children: [],
	        };
	    };
	    const root = openBranch(0);
	    if (!root) {
	        diagnostics.push({
	            severity: 'warning',
	            code: 'hierarchy-ownership-root-unframed',
	            streamPath,
	            offset: 0,
	            message: 'CFB hierarchy ownership: root body has no complete counted frame; ownership withheld',
	        });
	        return new Map();
	    }
	    const stack = [root];
	    while (stack.length > 0) {
	        const current = stack[stack.length - 1];
	        if (current.complete && current.remaining > 0) {
	            const childOffset = propertyEnd(current.cursor);
	            if (childOffset === undefined || childOffset + 19 > bytes.length || bytes[childOffset + 8] !== 0x42) {
	                current.complete = false;
	                continue;
	            }
	            current.remaining--;
	            const childKind = childOffset + 8;
	            if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, childKind + 9) !== 0) {
	                current.children.push(childKind);
	                const child = openBranch(childKind, current);
	                if (child === undefined) {
	                    current.complete = false;
	                }
	                else {
	                    stack.push(child);
	                }
	            }
	            else {
	                const end = childKind + 11 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, childKind + 1);
	                if (end <= childOffset + 19 || end > bytes.length) {
	                    current.complete = false;
	                }
	                else {
	                    current.leaves.push(childOffset);
	                    current.cursor = end;
	                }
	            }
	            continue;
	        }
	        stack.pop();
	        const resolved = current.complete && current.valid && current.name !== undefined;
	        for (const offset of current.children) {
	            parents.set(offset, {
	                viewName: current.name,
	                branchOffset: current.kindOffset,
	                state: resolved ?
	                    OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved
	                    : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
	            });
	        }
	        branches.push({
	            offset: current.kindOffset,
	            viewName: current.name,
	            state: !current.complete ? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Incomplete
	                : resolved ? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Resolved
	                    : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Withheld,
	        });
	        if (!current.complete) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-ownership-frame-withheld',
	                streamPath,
	                offset: current.kindOffset,
	                message: `CFB hierarchy ownership: view ${JSON.stringify(current.name) ?? '<undecodable>'} has an incomplete child list; every direct ownership claim withheld`,
	            });
	        }
	        for (const offset of current.leaves) {
	            scopes.set(offset, {
	                viewName: current.name,
	                branchOffset: current.kindOffset,
	                state: resolved ?
	                    OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved
	                    : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
	            });
	        }
	        if (current.name !== undefined) {
	            if (resolved) {
	                const path = JSON.stringify([streamPath, ...current.offsets]);
	                const paths = namedPaths.get(current.name.toUpperCase()) ?? new Set();
	                paths.add(path);
	                namedPaths.set(current.name.toUpperCase(), paths);
	                if (paths.size > 1) {
	                    diagnostics.push({
	                        severity: 'warning',
	                        code: 'hierarchy-ownership-name-collision',
	                        streamPath,
	                        offset: current.kindOffset,
	                        message: `CFB hierarchy ownership: view ${JSON.stringify(current.name)} occurs at distinct framed paths ${JSON.stringify([...paths])}; all applicable occurrence section evidence must agree`,
	                    });
	                }
	            }
	        }
	        const parent = stack[stack.length - 1];
	        if (parent !== undefined) {
	            if (current.complete) {
	                parent.cursor = current.cursor;
	            }
	            else {
	                parent.complete = false;
	            }
	        }
	    }
	    return scopes;
	}
	
	return OrcadDsnCfbHierarchyScopes;
}

var hasRequiredOrcadDsnCfbStreamParsing;

function requireOrcadDsnCfbStreamParsing () {
	if (hasRequiredOrcadDsnCfbStreamParsing) return OrcadDsnCfbStreamParsing;
	hasRequiredOrcadDsnCfbStreamParsing = 1;
	Object.defineProperty(OrcadDsnCfbStreamParsing, "__esModule", { value: true });
	OrcadDsnCfbStreamParsing.summarizeOrCadDsnCfbStream = summarizeOrCadDsnCfbStream;
	const DsnCfbBinarySpec_1 = requireDsnCfbBinarySpec();
	const OrcadDsnCfbRecordDecoders_1 = requireOrcadDsnCfbRecordDecoders();
	const OrcadDsnCfbStreamSpecs_1 = requireOrcadDsnCfbStreamSpecs();
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
	const OrcadDsnCfbCacheSourceLibraries_1 = requireOrcadDsnCfbCacheSourceLibraries();
	const OrcadDsnCfbHierarchyPhysicalNames_1 = requireOrcadDsnCfbHierarchyPhysicalNames();
	const OrcadDsnCfbOccurrenceFrames_1 = requireOrcadDsnCfbOccurrenceFrames();
	const OrcadDsnCfbHierarchyScopes_1 = requireOrcadDsnCfbHierarchyScopes();
	const OrcadDsnCfbPayloadIndex_1 = requireOrcadDsnCfbPayloadIndex();
	function stringsInPayload(bytes) {
	    return OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(bytes, {
	        maxCount: 64,
	        maxLength: 512,
	        requireNullTerminator: true,
	    });
	}
	function parseDsnStream(bytes) {
	    const jsonOffset = bytes.indexOf(0x7b);
	    const jsonEnd = bytes.lastIndexOf(0x7d);
	    if (jsonOffset === -1 || jsonEnd <= jsonOffset) {
	        return undefined;
	    }
	    try {
	        const text = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, jsonOffset, jsonEnd + 1);
	        return {
	            kind: 'dsn-stream',
	            jsonOffset,
	            json: JSON.parse(text),
	        };
	    }
	    catch {
	        return undefined;
	    }
	}
	function viewNameFromSchematicPath(path) {
	    const match = /^Root Entry\/Views\/([^/]+)\/Schematic$/.exec(path);
	    return match?.[1];
	}
	function parseSchematicStream(path, bytes, strings, pageStreamNames) {
	    const viewName = viewNameFromSchematicPath(path);
	    if (!viewName || strings.length === 0) {
	        return undefined;
	    }
	    const schematicName = strings[0]?.value;
	    const pageNames = strings
	        .slice(1)
	        .map((entry) => entry.value)
	        .filter((value) => pageStreamNames.has(`Root Entry/Views/${viewName}/Pages/${value}`));
	    let pageCountHint;
	    if (strings[0]) {
	        const firstStringEnd = strings[0].offset + 2 + strings[0].length + (strings[0].hasNullTerminator ? 1 : 0);
	        const scanEnd = Math.min(firstStringEnd + 16, bytes.length - 1);
	        for (let offset = firstStringEnd; offset < scanEnd; offset++) {
	            const candidate = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	            if (candidate === pageNames.length) {
	                pageCountHint = candidate;
	                break;
	            }
	        }
	    }
	    return {
	        kind: 'schematic',
	        viewName,
	        schematicName,
	        pageCountHint,
	        pageNames,
	    };
	}
	const INSTANCE_MARKER_PATTERN = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.LIBRARY_INSTANCE_MARKER_PATTERN;
	const isDecodedObjectId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.isDecodedObjectId;
	function recordedRootNameFromLibraryTail(bytes) {
	    if (bytes.length < 4 || bytes[bytes.length - 1] !== 0) {
	        return undefined;
	    }
	    const earliestOffset = Math.max(0, bytes.length - 0xffff - 3);
	    const candidates = [];
	    for (let offset = earliestOffset; offset <= bytes.length - 4; offset++) {
	        const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
	        if (offset + 2 + length + 1 !== bytes.length) {
	            continue;
	        }
	        const field = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, offset, {
	            maxLength: 0xffff,
	            requireNullTerminator: true,
	        });
	        if (field !== undefined) {
	            candidates.push(field.value);
	        }
	    }
	    return candidates.length === 1 ? candidates[0] : undefined;
	}
	function parseLibraryStream(path, bytes, diagnostics) {
	    if (path !== 'Root Entry/Library') {
	        return undefined;
	    }
	    const strings = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(bytes, {
	        maxCount: Number.MAX_SAFE_INTEGER,
	    });
	    const uniqueStrings = new Set(strings.map((string) => string.value));
	    const stringCatalog = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLibraryStringCatalog(bytes);
	    const recordedRootName = recordedRootNameFromLibraryTail(bytes);
	    return {
	        kind: 'library',
	        countedStringCount: strings.length,
	        uniqueStringCount: uniqueStrings.size,
	        propertyNames: [],
	        firstStrings: strings.slice(0, 50),
	        componentCandidateRecords: componentCandidateRecordsFromLibraryStrings(strings, path, diagnostics),
	        ...(stringCatalog ? { stringCatalog } : {}),
	        ...(recordedRootName !== undefined ? { recordedRootName } : {}),
	    };
	}
	function componentCandidateRecordsFromLibraryStrings(strings, streamPath, diagnostics) {
	    const records = [];
	    let blockStart = 0;
	    let blockValues = [];
	    let insideRun = false;
	    for (let index = 0; index < strings.length; index++) {
	        const string = strings[index];
	        if (!INSTANCE_MARKER_PATTERN.test(string.value)) {
	            if (insideRun) {
	                blockStart = index;
	                insideRun = false;
	            }
	            continue;
	        }
	        if (!insideRun) {
	            blockValues = strings.slice(blockStart, index).map((entry) => entry.value);
	            insideRun = true;
	        }
	        records.push({
	            offset: string.offset,
	            name: string.value,
	            values: [...blockValues, string.value],
	            properties: [],
	        });
	    }
	    if (records.length > 0) {
	        diagnostics.push({
	            severity: 'warning',
	            code: 'library-catalog-columns-unnamed',
	            message: `Library catalog column names could not be decoded; ${records.length} instance rows carry unnamed values`,
	            streamPath,
	        });
	    }
	    return records;
	}
	function parseDirectoryStream(path, bytes) {
	    const directoryKind = path === 'Root Entry/Parts Directory' ? 'parts-directory'
	        : path === 'Root Entry/Packages Directory' ? 'packages-directory'
	            : undefined;
	    if (!directoryKind) {
	        return undefined;
	    }
	    const entries = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(bytes, {
	        maxCount: Number.MAX_SAFE_INTEGER,
	        maxLength: 512,
	    }).map((string) => {
	        const viewSuffix = /^(.*)\.([^.]+)$/.exec(string.value);
	        return {
	            offset: string.offset,
	            name: string.value,
	            ...(directoryKind === 'parts-directory' && viewSuffix ?
	                {
	                    packageName: viewSuffix[1],
	                    viewName: viewSuffix[2],
	                }
	                : {}),
	        };
	    });
	    return {
	        kind: directoryKind,
	        entries,
	        entryCount: entries.length,
	    };
	}
	function parseVariantTextStream(path, asciiRuns) {
	    if (!path.includes('/VariantStore/') || asciiRuns.length === 0) {
	        return undefined;
	    }
	    const rows = asciiRuns
	        .map((run) => ({
	        offset: run.offset,
	        values: run.value.includes('^') ? run.value.split('^') : [run.value],
	    }))
	        .filter((row) => row.values.length > 0 && row.values.some((value) => value.length > 0));
	    if (rows.length === 0) {
	        return undefined;
	    }
	    let currentHeader;
	    for (const row of rows) {
	        if (row.values.length > 1 && row.values.some((value) => /part|footprint|value|name/i.test(value))) {
	            currentHeader = row.values;
	            continue;
	        }
	        if (currentHeader && row.values.length === currentHeader.length) {
	            row.properties = currentHeader
	                .map((name, index) => ({
	                name,
	                value: row.values[index] ?? '',
	                source: 'variant-update',
	            }))
	                .filter((property) => property.value.length > 0);
	        }
	    }
	    return {
	        kind: 'variant-text',
	        rows,
	        rowCount: rows.length,
	        ...(currentHeader ? { header: currentHeader } : {}),
	    };
	}
	function parseHierarchyStream(path, bytes, diagnostics) {
	    const match = /^Root Entry\/Views\/([^/]+)\/Hierarchy\/Hierarchy$/.exec(path);
	    if (!match) {
	        return undefined;
	    }
	    const entries = [];
	    const records = [];
	    const payloads = new Map();
	    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (payload) => payloads.set(payload.offset, payload));
	    const payloadIndex = new OrcadDsnCfbPayloadIndex_1.OrcadDsnCfbPayloadIndex([...payloads.values()]);
	    const ownershipBranches = [];
	    const parentScopes = new Map();
	    const owningViews = (0, OrcadDsnCfbHierarchyScopes_1.readHierarchyOccurrenceScopes)(bytes, match[1], diagnostics, path, ownershipBranches, parentScopes);
	    const inlineChildContainers = new Set();
	    for (const kindOffset of parentScopes.keys()) {
	        const container = payloadIndex.findUniqueContainingPayload(kindOffset - 8);
	        if (container !== undefined && container.offset !== kindOffset - 8) {
	            inlineChildContainers.add(container.offset);
	        }
	    }
	    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (payload) => {
	        const strings = stringsInPayload(payload.payload);
	        const recordDbId = payload.payload.length >= 8 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 4) : undefined;
	        const firstString = strings[0];
	        records.push({
	            recordId: payload.payload.length >= 4 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0) : 0,
	            ...(isDecodedObjectId(recordDbId) ? { dbId: recordDbId } : {}),
	            ...(firstString ? { name: firstString.value, nameOffset: firstString.offset } : {}),
	        });
	        const nameString = strings.find((string) => string.offset >= 8);
	        if (payload.payload[8] === 0x42) {
	            const occurrence = (0, OrcadDsnCfbOccurrenceFrames_1.readHierarchyOccurrenceFrame)(bytes, payload, payloads, owningViews.get(payload.offset)?.state === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved);
	            if (occurrence) {
	                entries.push({
	                    offset: payload.offset,
	                    recordId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0),
	                    occurrenceId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0),
	                    refInstId: recordDbId,
	                    dbId: recordDbId,
	                    name: occurrence.name,
	                    referenceStringOffset: occurrence.nameOffset,
	                    sectionSelectorStringIndex: occurrence.selectorIndex,
	                    owningViewName: owningViews.get(payload.offset)?.viewName,
	                    ownershipBranchOffset: owningViews.get(payload.offset)?.branchOffset,
	                    ownershipState: owningViews.get(payload.offset)?.state ?? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
	                });
	                return;
	            }
	            else {
	                diagnostics.push({
	                    severity: 'warning',
	                    code: 'hierarchy-occurrence-frame-withheld',
	                    streamPath: path,
	                    offset: payload.offset,
	                    message: `CFB occurrence [hierarchy-occurrence-frame-withheld] refInstId=${recordDbId}: incomplete or unsupported occurrence frame`,
	                });
	                const scope = owningViews.get(payload.offset);
	                if (scope !== undefined) {
	                    entries.push({
	                        offset: payload.offset,
	                        recordId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0),
	                        ...(isDecodedObjectId(recordDbId) ? { refInstId: recordDbId } : {}),
	                        owningViewName: scope?.viewName,
	                        ownershipBranchOffset: scope?.branchOffset,
	                        ownershipState: scope?.state ?? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
	                    });
	                    return;
	                }
	            }
	        }
	        if (!nameString) {
	            return;
	        }
	        const referenceString = strings.find((string) => string.offset > nameString.offset && string.value.length > 1 && string.value !== 'X');
	        const dbId = payload.payload.length >= 8 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 4) : undefined;
	        const recordId = payload.payload.length >= 4 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0) : 0;
	        const parent = parentScopes.get(payload.offset + 8);
	        const child = ownershipBranches.find((branch) => branch.offset === payload.offset + 8);
	        if (parent !== undefined && child?.viewName !== undefined) {
	            entries.push({
	                offset: payload.offset,
	                recordId,
	                ...(isDecodedObjectId(dbId) ? { dbId } : {}),
	                name: child.viewName,
	                contentsViewName: child.viewName,
	                referenceStringOffset: child.offset + 9,
	                parentViewName: parent.viewName,
	                parentBranchOffset: parent.branchOffset,
	            });
	            return;
	        }
	        if (inlineChildContainers.has(payload.offset)) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-payload-reference-withheld',
	                streamPath: path,
	                offset: payload.offset,
	                message: 'CFB hierarchy: enclosing payload is not a child header; inline child strings cannot supply its reference or identity',
	            });
	            return;
	        }
	        entries.push({
	            offset: payload.offset,
	            recordId,
	            ...(isDecodedObjectId(dbId) ? { dbId } : {}),
	            name: nameString.value,
	            ...(referenceString ? { reference: referenceString.value } : {}),
	            contentsViewName: nameString.value,
	            ...(parent === undefined ? {} : { parentViewName: parent.viewName, parentBranchOffset: parent.branchOffset }),
	        });
	    });
	    for (const [kindOffset, parent] of parentScopes) {
	        const offset = kindOffset - 8;
	        if (entries.some((entry) => entry.offset === offset && entry.parentBranchOffset !== undefined)) {
	            continue;
	        }
	        const child = ownershipBranches.find((branch) => branch.offset === kindOffset);
	        const container = payloadIndex.findUniqueContainingPayload(offset);
	        if (child?.viewName === undefined ||
	            container === undefined ||
	            kindOffset + 11 > container.offset + container.payloadByteLength) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-child-container-withheld',
	                streamPath: path,
	                offset,
	                message: 'CFB hierarchy: counted child header has no decoded name or unique enclosing payload; child reference withheld',
	            });
	            continue;
	        }
	        const dbId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
	        entries.push({
	            offset,
	            recordId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset),
	            ...(isDecodedObjectId(dbId) ? { dbId } : {}),
	            name: child.viewName,
	            contentsViewName: child.viewName,
	            referenceStringOffset: kindOffset + 9,
	            parentViewName: parent.viewName,
	            parentBranchOffset: parent.branchOffset,
	        });
	    }
	    for (const [offset, scope] of owningViews) {
	        if (payloads.has(offset)) {
	            continue;
	        }
	        const container = payloadIndex.findUniqueContainingPayload(offset);
	        const recordId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	        const refInstId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
	        if (container === undefined) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-occurrence-container-withheld',
	                streamPath: path,
	                offset,
	                message: 'CFB occurrence: counted child has no unique enclosing payload; ownership tombstone retained',
	            });
	            entries.push({
	                offset,
	                recordId,
	                ...(isDecodedObjectId(refInstId) ? { refInstId } : {}),
	                owningViewName: scope.viewName,
	                ownershipBranchOffset: scope.branchOffset,
	                ownershipState: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
	            });
	            continue;
	        }
	        const payload = {
	            offset,
	            payloadByteLength: container.offset + container.payloadByteLength - offset,
	            byteLength: container.offset + container.byteLength - offset,
	            payload: bytes.subarray(offset, container.offset + container.payloadByteLength),
	        };
	        const occurrence = (0, OrcadDsnCfbOccurrenceFrames_1.readHierarchyOccurrenceFrame)(bytes, payload, payloads, scope.state === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved);
	        if (!occurrence) {
	            diagnostics.push({
	                severity: 'warning',
	                code: 'hierarchy-occurrence-frame-withheld',
	                streamPath: path,
	                offset,
	                message: 'CFB occurrence: structurally owned inline occurrence has incomplete annotation framing',
	            });
	        }
	        entries.push({
	            offset,
	            recordId,
	            ...(occurrence !== undefined || isDecodedObjectId(recordId) ? { occurrenceId: recordId } : {}),
	            ...(occurrence !== undefined || isDecodedObjectId(refInstId) ? { refInstId, dbId: refInstId } : {}),
	            name: occurrence?.name,
	            referenceStringOffset: occurrence?.nameOffset,
	            sectionSelectorStringIndex: occurrence?.selectorIndex,
	            owningViewName: scope.viewName,
	            ownershipBranchOffset: scope.branchOffset,
	            ownershipState: scope.state,
	        });
	    }
	    entries.sort((left, right) => left.offset - right.offset);
	    return {
	        kind: 'hierarchy',
	        ownershipBranches,
	        viewName: match[1],
	        physicalNetNames: (0, OrcadDsnCfbHierarchyPhysicalNames_1.readHierarchyPhysicalNetNames)(bytes, match[1], path, diagnostics),
	        entries,
	        entryCount: entries.length,
	        records,
	    };
	}
	function readCisStringList(bytes, offset, count, maxLength) {
	    if (count < 0 || count > 512) {
	        return undefined;
	    }
	    const values = [];
	    let cursor = offset;
	    for (let index = 0; index < count; index++) {
	        const string = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength });
	        if (!string) {
	            return undefined;
	        }
	        values.push(string.value);
	        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string);
	    }
	    return { values, offset: cursor };
	}
	function readCisPropertyValues(bytes, offset, count, maxLength) {
	    if (count < 0 || count > 512) {
	        return undefined;
	    }
	    const values = [];
	    let cursor = offset;
	    for (let index = 0; index < count; index++) {
	        if (cursor + 2 <= bytes.length && OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor) === 0) {
	            values.push('');
	            cursor += cursor + 2 < bytes.length && bytes[cursor + 2] === 0 ? 3 : 2;
	            continue;
	        }
	        const string = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength });
	        if (!string) {
	            return undefined;
	        }
	        values.push(string.value);
	        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string);
	    }
	    return { values, offset: cursor };
	}
	function parseCisSchematicRecord(bytes, offset) {
	    const start = offset;
	    if (offset + 16 > bytes.length) {
	        return undefined;
	    }
	    const recordType = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	    const recordId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
	    const occurrenceNameCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 8);
	    offset += 12;
	    if (recordType > 10_000 || recordId > 1_000_000_000 || occurrenceNameCount < 1 || occurrenceNameCount > 128) {
	        return undefined;
	    }
	    const occurrenceNames = readCisStringList(bytes, offset, occurrenceNameCount, 512);
	    if (!occurrenceNames || occurrenceNames.offset + 4 > bytes.length) {
	        return undefined;
	    }
	    offset = occurrenceNames.offset;
	    const qualifierCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	    offset += 4;
	    const qualifiers = readCisStringList(bytes, offset, qualifierCount, 512);
	    if (!qualifiers || qualifiers.offset + 8 > bytes.length) {
	        return undefined;
	    }
	    offset = qualifiers.offset;
	    const propertyGroup = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	    const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
	    offset += 8;
	    if (propertyGroup > 10_000 || propertyCount < 1 || propertyCount > 512) {
	        return undefined;
	    }
	    const propertyNames = readCisStringList(bytes, offset, propertyCount, 512);
	    if (!propertyNames || propertyNames.offset + 4 > bytes.length) {
	        return undefined;
	    }
	    offset = propertyNames.offset;
	    const propertyValueCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	    offset += 4;
	    if (propertyValueCount !== propertyNames.values.length) {
	        return undefined;
	    }
	    const propertyValues = readCisPropertyValues(bytes, offset, propertyValueCount, 4096);
	    if (!propertyValues) {
	        return undefined;
	    }
	    offset = propertyValues.offset;
	    const properties = propertyNames.values.map((name, index) => ({
	        name,
	        value: propertyValues.values[index] ?? '',
	    }));
	    return {
	        offset: start,
	        byteLength: offset - start,
	        recordType,
	        recordId,
	        occurrenceNames: occurrenceNames.values,
	        qualifiers: qualifiers.values,
	        propertyGroup,
	        propertyNames: propertyNames.values,
	        properties,
	        populatedProperties: properties.filter((property) => property.value.length > 0),
	    };
	}
	function cisPropertyRecordFromNamesAndValues(offset, byteLength, recordType, recordId, occurrenceNames, qualifiers, propertyGroup, propertyNames, propertyValues) {
	    const properties = propertyNames.map((name, index) => ({
	        name,
	        value: propertyValues[index] ?? '',
	    }));
	    return {
	        offset,
	        byteLength,
	        recordType,
	        recordId,
	        occurrenceNames,
	        qualifiers,
	        propertyGroup,
	        propertyNames,
	        properties,
	        populatedProperties: properties.filter((property) => property.value.length > 0),
	    };
	}
	function parseCisSchematicPropertyOnlyRecord(bytes, offset) {
	    const start = offset;
	    if (offset + 4 > bytes.length) {
	        return undefined;
	    }
	    const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	    offset += 4;
	    if (propertyCount < 1 || propertyCount > 512) {
	        return undefined;
	    }
	    const propertyNames = readCisStringList(bytes, offset, propertyCount, 512);
	    if (!propertyNames || propertyNames.offset + 4 > bytes.length) {
	        return undefined;
	    }
	    offset = propertyNames.offset;
	    const propertyValueCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
	    offset += 4;
	    if (propertyValueCount !== propertyNames.values.length) {
	        return undefined;
	    }
	    const propertyValues = readCisPropertyValues(bytes, offset, propertyValueCount, 4096);
	    if (!propertyValues) {
	        return undefined;
	    }
	    offset = propertyValues.offset;
	    return cisPropertyRecordFromNamesAndValues(start, offset - start, 0, 0, [], [], 0, propertyNames.values, propertyValues.values);
	}
	function parseCisSchematicInfoStream(path, bytes) {
	    const match = /^Root Entry\/Views\/([^/]+)\/CISSchematic\/SchematicInfoStorage\/([^/]+)$/.exec(path);
	    if (!match || match[2] === 'SchematicInfoStream') {
	        return undefined;
	    }
	    const records = [];
	    let offset = 0;
	    while (offset < bytes.length) {
	        const record = parseCisSchematicRecord(bytes, offset) ?? parseCisSchematicPropertyOnlyRecord(bytes, offset);
	        if (!record) {
	            break;
	        }
	        records.push(record);
	        offset += record.byteLength;
	    }
	    if (records.length === 0) {
	        return undefined;
	    }
	    return {
	        kind: 'cis-schematic-info',
	        viewName: match[1],
	        pageName: match[2],
	        recordCount: records.length,
	        propertyNames: Array.from(new Set(records.flatMap((record) => record.propertyNames))).sort(),
	        records,
	    };
	}
	function primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
	    return {
	        countedStringCount: strings.length,
	        asciiRunCount: asciiRuns.length,
	        ...(OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPrimitives(decodedPrimitives.counts) ?
	            {
	                decodedPrimitiveCounts: decodedPrimitives.counts,
	                decodedPrimitiveByteCount: decodedPrimitives.decodedByteCount,
	            }
	            : {}),
	        ...(decodedSymbolObjects && OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedSymbolObjects(decodedSymbolObjects.counts) ?
	            {
	                decodedSymbolObjectCounts: decodedSymbolObjects.counts,
	                decodedSymbolObjectByteCount: decodedSymbolObjects.decodedByteCount,
	            }
	            : {}),
	    };
	}
	function pageObjectStreamSummary(decodedPageObjects) {
	    return {
	        ...(OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPageObjects(decodedPageObjects.counts) ?
	            {
	                decodedPageObjectCounts: decodedPageObjects.counts,
	                decodedPageObjectByteCount: decodedPageObjects.decodedByteCount,
	            }
	            : {}),
	    };
	}
	function parsePageHeader(bytes, strings) {
	    const pageName = strings[0];
	    if (!pageName) {
	        return {};
	    }
	    const pageNameEnd = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pageName);
	    const pageSize = strings.find((string) => string.offset === pageNameEnd && string.length <= 16);
	    if (!pageSize) {
	        return {};
	    }
	    const metadataOffset = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pageSize);
	    const pageDateTime = metadataOffset + 8 <= bytes.length ?
	        {
	            createDateEpoch: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset),
	            modifyDateEpoch: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 4),
	        }
	        : undefined;
	    const pageSizeInfo = metadataOffset + 36 <= bytes.length ?
	        {
	            isMetric: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 20) !== 0,
	            width: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 24),
	            height: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 28),
	            pinToPin: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 32),
	        }
	        : undefined;
	    return {
	        pageSize: pageSize.value,
	        ...(pageDateTime && pageDateTime.createDateEpoch > 0 && pageDateTime.modifyDateEpoch > 0 ? { pageDateTime } : {}),
	        ...((pageSizeInfo &&
	            pageSizeInfo.width > 0 &&
	            pageSizeInfo.height > 0 &&
	            pageSizeInfo.pinToPin > 0 &&
	            pageSizeInfo.width <= 1_000_000 &&
	            pageSizeInfo.height <= 1_000_000 &&
	            pageSizeInfo.pinToPin <= 10_000) ?
	            { pageSizeInfo }
	            : {}),
	    };
	}
	function parseCacheStream(path, strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
	    if (path !== 'Root Entry/Cache') {
	        return undefined;
	    }
	    return {
	        kind: 'cache',
	        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects),
	    };
	}
	function parsePackageStream(path, strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
	    const match = /^Root Entry\/Packages\/([^/]+)$/.exec(path);
	    if (!match) {
	        return undefined;
	    }
	    return {
	        kind: 'package',
	        packageName: match[1],
	        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects),
	    };
	}
	function parseSymbolStream(path, strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
	    const match = /^Root Entry\/Symbols\/([^/]+)$/.exec(path);
	    if (!match) {
	        return undefined;
	    }
	    return {
	        kind: 'symbol',
	        symbolName: match[1],
	        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects),
	    };
	}
	function parsePageStream(path, bytes, strings, asciiRuns, decodedPrimitives, decodedPageObjects) {
	    const match = /^Root Entry\/Views\/([^/]+)\/Pages\/([^/]+)$/.exec(path);
	    if (!match) {
	        return undefined;
	    }
	    return {
	        kind: 'page',
	        viewName: match[1],
	        pageName: strings[0]?.value ?? match[2],
	        ...parsePageHeader(bytes, strings),
	        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives),
	        ...pageObjectStreamSummary(decodedPageObjects),
	    };
	}
	function parseTextStream(path, asciiRuns) {
	    if (!/\.txt$/i.test(path) || asciiRuns.length === 0) {
	        return undefined;
	    }
	    return {
	        kind: 'text',
	        text: asciiRuns.map((run) => run.value).join('\n'),
	    };
	}
	function summarizeOrCadDsnCfbStream(entry, pageStreamNames, options, libraryStringCatalog) {
	    if (entry.type !== 2 || !entry.content) {
	        return undefined;
	    }
	    const streamSpecs = DsnCfbBinarySpec_1.DsnCfbBinarySpec.matchStreamSpecs(OrcadDsnCfbStreamSpecs_1.ORCAD_DSN_CFB_STREAM_SPECS, entry);
	    const leadingRecords = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.detectLeadingRecordHeaders(entry.content);
	    const lengthPrefixedStrings = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(entry.content, {
	        maxCount: Number.MAX_SAFE_INTEGER,
	    });
	    const diagnosticAsciiRuns = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractAsciiRuns(entry.content, {
	        minLength: options.minDiagnosticAsciiRunLength,
	        maxCount: options.maxDiagnosticAsciiRunsPerStream,
	    });
	    const needsTextAsciiRuns = /\.txt$/i.test(entry.path) || entry.path.includes('/VariantStore/');
	    const textAsciiRuns = needsTextAsciiRuns ?
	        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractAsciiRuns(entry.content, {
	            minLength: 1,
	            maxCount: Number.MAX_SAFE_INTEGER,
	        })
	        : diagnosticAsciiRuns;
	    const decodedPrimitives = OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords(entry.content, {
	        streamPath: entry.path,
	        streamName: entry.name,
	        includeDiagnostics: options.includeDiagnostics,
	        includeRawRecords: options.includeRawRecords,
	    });
	    const statesSheetSymbolMetadata = /^Root Entry\/(?:Packages\/[^/]+|Cache)$/.test(entry.path);
	    const sheetSymbolMetadataRecords = statesSheetSymbolMetadata ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractSheetSymbolMetadataRecords(entry.content) : undefined;
	    const isPageStream = streamSpecs.some((spec) => spec.name === 'page');
	    const isSymbolObjectStream = streamSpecs.some((spec) => spec.name === 'cache' || spec.name === 'package' || spec.name === 'symbol');
	    const contextualCatalog = options.extra.libraryStringCatalog;
	    const contextualLibraryStringCatalog = Array.isArray(contextualCatalog) && contextualCatalog.every((value) => typeof value === 'string') ?
	        contextualCatalog
	        : undefined;
	    const resolvedLibraryStringCatalog = libraryStringCatalog ?? contextualLibraryStringCatalog;
	    const decodedPageObjects = isPageStream ?
	        OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPageObjects(entry.content, {
	            libraryStringCatalog: resolvedLibraryStringCatalog,
	            streamPath: entry.path,
	            streamName: entry.name,
	            includeDiagnostics: options.includeDiagnostics,
	            includeRawRecords: options.includeRawRecords,
	            extra: options.extra,
	        })
	        : undefined;
	    const decodedSymbolObjects = isSymbolObjectStream ?
	        OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbSymbolObjects(entry.content, {
	            streamName: entry.name,
	            streamPath: entry.path,
	            includeDiagnostics: options.includeDiagnostics,
	            includeRawRecords: options.includeRawRecords,
	        })
	        : undefined;
	    const diagnostics = [
	        ...(decodedPrimitives.diagnostics ?? []),
	        ...(decodedPageObjects?.diagnostics ?? []),
	        ...(decodedSymbolObjects?.diagnostics ?? []),
	    ];
	    const cacheSourceLibraries = entry.path === 'Root Entry/Cache' ?
	        (0, OrcadDsnCfbCacheSourceLibraries_1.decodeCacheSourceLibraries)(entry.content, decodedSymbolObjects?.records ?? [], diagnostics, decodedPrimitives.records)
	        : undefined;
	    const undecodedFrames = diagnostics.flatMap((diagnostic) => (diagnostic.code === 'cache-primitive-undecoded' &&
	        diagnostic.offset !== undefined &&
	        typeof diagnostic.details?.byteLength === 'number') ?
	        [{ start: diagnostic.offset, end: diagnostic.offset + diagnostic.details.byteLength }]
	        : []);
	    const decisionDiagnostics = options.extra.roleDiagnostics;
	    decodedPrimitives.records = decodedPrimitives.records.filter((primitive) => {
	        if (!undecodedFrames.some((frame) => primitive.offset >= frame.start && primitive.offset < frame.end)) {
	            return true;
	        }
	        decodedPrimitives.counts[primitive.kind]--;
	        decodedPrimitives.decodedByteCount -= primitive.byteLength;
	        return false;
	    });
	    const decodedRecords = [
	        ...(decodedPrimitives.decodedRecords ?? []).filter((record) => !undecodedFrames.some((frame) => record.span.offset >= frame.start && record.span.offset < frame.end)),
	        ...(decodedPageObjects?.decodedRecords ?? []),
	        ...(decodedSymbolObjects?.decodedRecords ?? []),
	    ];
	    const stringOffsets = new Set(lengthPrefixedStrings.map((field) => field.offset));
	    for (const source of cacheSourceLibraries ?? []) {
	        for (const field of [source.library, source.continuationLibrary]) {
	            if (field && !stringOffsets.has(field.offset)) {
	                lengthPrefixedStrings.push(field);
	                stringOffsets.add(field.offset);
	            }
	        }
	    }
	    lengthPrefixedStrings.sort((left, right) => left.offset - right.offset);
	    const parsed = (entry.path === 'Root Entry/DsnStream' ? parseDsnStream(entry.content) : undefined) ??
	        parseSchematicStream(entry.path, entry.content, lengthPrefixedStrings, pageStreamNames) ??
	        parseLibraryStream(entry.path, entry.content, diagnostics) ??
	        parseDirectoryStream(entry.path, entry.content) ??
	        parseHierarchyStream(entry.path, entry.content, diagnostics) ??
	        parseCisSchematicInfoStream(entry.path, entry.content) ??
	        parseVariantTextStream(entry.path, textAsciiRuns) ??
	        parseCacheStream(entry.path, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedSymbolObjects) ??
	        parsePackageStream(entry.path, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedSymbolObjects) ??
	        parseSymbolStream(entry.path, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedSymbolObjects) ??
	        (decodedPageObjects ?
	            parsePageStream(entry.path, entry.content, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedPageObjects)
	            : undefined) ??
	        parseTextStream(entry.path, textAsciiRuns);
	    for (const diagnostic of diagnostics) {
	        if (diagnostic.code.startsWith('typed-frame-') ||
	            diagnostic.code === 'cache-primitive-undecoded' ||
	            diagnostic.code.startsWith('cache-source-library-') ||
	            diagnostic.code === 'hierarchy-physical-name-frame-withheld' ||
	            diagnostic.code === 'hierarchy-occurrence-frame-withheld' ||
	            diagnostic.code === 'hierarchy-occurrence-container-withheld' ||
	            diagnostic.code === 'hierarchy-child-container-withheld' ||
	            diagnostic.code === 'hierarchy-payload-reference-withheld' ||
	            diagnostic.code.startsWith('hierarchy-ownership-') ||
	            diagnostic.code === 'placement-reference-tail-withheld' ||
	            diagnostic.code === 'placement-value-withheld' ||
	            diagnostic.code === 'cache-view-bounds-withheld' ||
	            diagnostic.code === 'cache-pin-frame-withheld' ||
	            diagnostic.code === 'page-pin-frame-withheld' ||
	            diagnostic.code.startsWith('wire-alias-child-') ||
	            diagnostic.code === 'page-property-frame-withheld') {
	            decisionDiagnostics?.push(diagnostic);
	        }
	    }
	    return {
	        path: entry.path,
	        name: entry.name,
	        size: entry.size,
	        storagePath: entry.storagePath,
	        leadingRecords,
	        lengthPrefixedStrings,
	        asciiRuns: diagnosticAsciiRuns,
	        ...(cacheSourceLibraries ? { cacheSourceLibraries } : {}),
	        ...(sheetSymbolMetadataRecords ? { sheetSymbolMetadataRecords } : {}),
	        ...(OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPrimitives(decodedPrimitives.counts) ?
	            {
	                decodedPrimitives: decodedPrimitives.records,
	                decodedPrimitiveCounts: decodedPrimitives.counts,
	                decodedPrimitivesTruncated: decodedPrimitives.truncated,
	                decodedPrimitiveByteCount: decodedPrimitives.decodedByteCount,
	            }
	            : {}),
	        ...(decodedPageObjects && OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPageObjects(decodedPageObjects.counts) ?
	            {
	                decodedPageObjects: decodedPageObjects.records,
	                decodedPageObjectCounts: decodedPageObjects.counts,
	                decodedPageObjectsTruncated: decodedPageObjects.truncated,
	                decodedPageObjectByteCount: decodedPageObjects.decodedByteCount,
	            }
	            : {}),
	        ...(decodedSymbolObjects && OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedSymbolObjects(decodedSymbolObjects.counts) ?
	            {
	                decodedSymbolObjects: decodedSymbolObjects.records,
	                decodedSymbolObjectCounts: decodedSymbolObjects.counts,
	                decodedSymbolObjectsTruncated: decodedSymbolObjects.truncated,
	                decodedSymbolObjectByteCount: decodedSymbolObjects.decodedByteCount,
	            }
	            : {}),
	        ...(options.includeRawRecords && decodedRecords.length > 0 ? { decodedRecords } : {}),
	        diagnostics: options.includeDiagnostics ? diagnostics : (diagnostics.filter((diagnostic) => diagnostic.code.startsWith('typed-frame-') ||
	            diagnostic.code.startsWith('hierarchy-ownership-') ||
	            [
	                'hierarchy-occurrence-frame-withheld',
	                'hierarchy-occurrence-container-withheld',
	                'hierarchy-ownership-frame-withheld',
	                'placement-reference-tail-withheld',
	                'placement-value-withheld',
	                'cache-view-bounds-withheld',
	                'cache-pin-frame-withheld',
	                'page-pin-frame-withheld',
	            ].includes(diagnostic.code))),
	        ...(parsed ? { parsed } : {}),
	    };
	}
	
	return OrcadDsnCfbStreamParsing;
}

var hasRequiredOrcadDsnCfbParser;

function requireOrcadDsnCfbParser () {
	if (hasRequiredOrcadDsnCfbParser) return OrcadDsnCfbParser;
	hasRequiredOrcadDsnCfbParser = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OrcadDsnCfbTypes = exports.OrcadDsnCfbStreamUtils = exports.OrcadDsnCfbRecordDecoders = exports.parseOrCadDsnCfbLogicalSchema = exports.OrcadDsnCfbLogicalSchemaConverter = exports.convertOrCadDsnCfbToLogicalSchema = exports.OrcadDsnCfbDeclarativeSpecs = exports.readOrCadDsnCfbEntries = void 0;
		exports.parseOrCadDsnCfb = parseOrCadDsnCfb;
		const OrcadDsnCfbCfb_1 = requireOrcadDsnCfbCfb();
		const OrcadDsnCfbMaterializer_1 = requireOrcadDsnCfbMaterializer();
		const OrcadDsnCfbOccurrenceOwnership_1 = requireOrcadDsnCfbOccurrenceOwnership();
		const OrcadDsnCfbStreamParsing_1 = requireOrcadDsnCfbStreamParsing();
		const DsnCfbBinarySpec_1 = requireDsnCfbBinarySpec();
		const OrcadDsnCfbStreamSpecs_1 = requireOrcadDsnCfbStreamSpecs();
		const OrcadDsnCfbRecordDecoders_1 = requireOrcadDsnCfbRecordDecoders();
		const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
		const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
		var OrcadDsnCfbCfb_2 = requireOrcadDsnCfbCfb();
		Object.defineProperty(exports, "readOrCadDsnCfbEntries", { enumerable: true, get: function () { return OrcadDsnCfbCfb_2.readOrCadDsnCfbEntries; } });
		var OrcadDsnCfbDeclarativeSpecs_1 = requireOrcadDsnCfbDeclarativeSpecs();
		Object.defineProperty(exports, "OrcadDsnCfbDeclarativeSpecs", { enumerable: true, get: function () { return OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs; } });
		var OrcadDsnCfbLogicalSchemaConverter_1 = requireOrcadDsnCfbLogicalSchemaConverter();
		Object.defineProperty(exports, "convertOrCadDsnCfbToLogicalSchema", { enumerable: true, get: function () { return OrcadDsnCfbLogicalSchemaConverter_1.convertOrCadDsnCfbToLogicalSchema; } });
		Object.defineProperty(exports, "OrcadDsnCfbLogicalSchemaConverter", { enumerable: true, get: function () { return OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter; } });
		Object.defineProperty(exports, "parseOrCadDsnCfbLogicalSchema", { enumerable: true, get: function () { return OrcadDsnCfbLogicalSchemaConverter_1.parseOrCadDsnCfbLogicalSchema; } });
		var OrcadDsnCfbRecordDecoders_2 = requireOrcadDsnCfbRecordDecoders();
		Object.defineProperty(exports, "OrcadDsnCfbRecordDecoders", { enumerable: true, get: function () { return OrcadDsnCfbRecordDecoders_2.OrcadDsnCfbRecordDecoders; } });
		var OrcadDsnCfbStreamUtils_2 = requireOrcadDsnCfbStreamUtils();
		Object.defineProperty(exports, "OrcadDsnCfbStreamUtils", { enumerable: true, get: function () { return OrcadDsnCfbStreamUtils_2.OrcadDsnCfbStreamUtils; } });
		var OrcadDsnCfbTypes_2 = requireOrcadDsnCfbTypes();
		Object.defineProperty(exports, "OrcadDsnCfbTypes", { enumerable: true, get: function () { return OrcadDsnCfbTypes_2.OrcadDsnCfbTypes; } });
		function collectKnownSpecialCacheSymbolNames(entries, diagnostics) {
		    const cacheEntry = entries.find((entry) => entry.type === 2 &&
		        entry.content &&
		        DsnCfbBinarySpec_1.DsnCfbBinarySpec.matchStreamSpecs(OrcadDsnCfbStreamSpecs_1.ORCAD_DSN_CFB_STREAM_SPECS, entry).some((spec) => spec.name === 'cache'));
		    if (!cacheEntry?.content) {
		        return new Map();
		    }
		    const decodedSymbols = OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbSymbolObjects(cacheEntry.content, {
		        streamPath: cacheEntry.path,
		        streamName: cacheEntry.name,
		        includeDiagnostics: true,
		    });
		    const decodedPrimitives = OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords(cacheEntry.content, {
		        streamPath: cacheEntry.path,
		        streamName: cacheEntry.name,
		        includeDiagnostics: true,
		    });
		    const symbolObjects = decodedSymbols.records;
		    const primitives = decodedPrimitives.records;
		    const decodeFailureOffsets = [...(decodedSymbols.diagnostics ?? []), ...(decodedPrimitives.diagnostics ?? [])]
		        .map((diagnostic) => diagnostic.offset)
		        .filter((offset) => offset !== undefined)
		        .sort((left, right) => left - right);
		    const combined = [
		        ...symbolObjects.map((record) => ({
		            offset: record.offset,
		            kind: record.kind,
		            name: 'name' in record ? record.name : undefined,
		            pinDisplayFlags: record.kind === 'symbol-pin' ? record.displayFlags : undefined,
		            pinType: record.kind === 'symbol-pin' ? record.pinType : undefined,
		        })),
		        ...primitives.map((record) => ({ offset: record.offset, kind: record.kind, name: undefined })),
		    ].sort((left, right) => left.offset - right.offset);
		    const namedRecordEvidence = new Map();
		    const directionVotes = new Map();
		    const packageNames = new Set();
		    let currentHeaderKind;
		    let currentHeaderName;
		    let currentHeaderOffset = 0;
		    let currentBodyPinCount = 0;
		    let currentBodyDirectedPins = 0;
		    let currentBodyDirectionlessPins = 0;
		    let currentBodyHasPowerPin = false;
		    const flushCurrentGroup = (groupEndOffset) => {
		        if (currentHeaderKind === 'named-record' && currentHeaderName !== undefined) {
		            const decodeIncomplete = decodeFailureOffsets.some((offset) => offset >= currentHeaderOffset && offset < groupEndOffset);
		            const previous = namedRecordEvidence.get(currentHeaderName);
		            const statesDirection = !decodeIncomplete && currentBodyDirectedPins + currentBodyDirectionlessPins > 0;
		            if (!statesDirection && currentBodyPinCount > 0) {
		                diagnostics?.push({
		                    severity: 'info',
		                    code: 'cache-symbol-direction-unread',
		                    message: `cache group named "${currentHeaderName}" states no readable pin direction, so it does not settle the connector kind`,
		                    streamPath: cacheEntry.path,
		                    offset: currentHeaderOffset,
		                });
		            }
		            const votes = directionVotes.get(currentHeaderName) ?? { directed: false, undirected: false };
		            if (statesDirection) {
		                votes.directed = votes.directed || currentBodyDirectedPins > 0;
		                votes.undirected = votes.undirected || currentBodyDirectedPins === 0;
		            }
		            directionVotes.set(currentHeaderName, votes);
		            const currentBodyHasDirectedPin = currentBodyDirectedPins > 0;
		            const drawsAPin = currentBodyPinCount > 0;
		            const previousDrawsAPin = (previous?.pinCount ?? 0) > 0;
		            const disagrees = drawsAPin &&
		                previousDrawsAPin &&
		                (previous?.hasDirectedConnectionPin !== currentBodyHasDirectedPin ||
		                    previous?.hasPowerConnectionPin !== currentBodyHasPowerPin);
		            namedRecordEvidence.set(currentHeaderName, {
		                pinCount: Math.max(previous?.pinCount ?? 0, currentBodyPinCount),
		                decodeIncomplete: (previous?.decodeIncomplete ?? true) && decodeIncomplete,
		                hasDirectedConnectionPin: votes.directed,
		                directionConflicted: votes.directed && votes.undirected,
		                hasPowerConnectionPin: drawsAPin && !previousDrawsAPin ? currentBodyHasPowerPin : ((previous?.hasPowerConnectionPin ?? currentBodyHasPowerPin)),
		                conflictingDefinitions: (previous?.conflictingDefinitions ?? false) || disagrees,
		            });
		        }
		        else if (currentHeaderKind === 'view-header' && currentHeaderName !== undefined) {
		            packageNames.add(currentHeaderName);
		        }
		    };
		    for (const record of combined) {
		        if (record.kind === 'view-header' || record.kind === 'named-record') {
		            flushCurrentGroup(record.offset);
		            currentHeaderKind = record.kind;
		            currentHeaderName = record.name;
		            currentHeaderOffset = record.offset;
		            currentBodyPinCount = 0;
		            currentBodyDirectedPins = 0;
		            currentBodyDirectionlessPins = 0;
		            currentBodyHasPowerPin = false;
		            continue;
		        }
		        if (currentHeaderKind && record.kind === 'symbol-pin') {
		            currentBodyPinCount++;
		            currentBodyHasPowerPin =
		                currentBodyHasPowerPin || record.pinType === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinType.Power;
		            if (record.pinDisplayFlags === undefined) {
		                continue;
		            }
		            if (isDirectedSymbolPin(record.pinDisplayFlags)) {
		                currentBodyDirectedPins++;
		            }
		            else {
		                currentBodyDirectionlessPins++;
		            }
		        }
		    }
		    flushCurrentGroup(cacheEntry.content.length);
		    for (const packageName of packageNames) {
		        namedRecordEvidence.delete(packageName);
		    }
		    return namedRecordEvidence;
		}
		function isDirectedSymbolPin(displayFlags) {
		    if (displayFlags === undefined) {
		        return false;
		    }
		    const directed = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinDisplayFlag.IsLeftPointing |
		        OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinDisplayFlag.IsRightPointing;
		    return (displayFlags & directed) !== 0;
		}
		function libraryStringCatalogFromExtra(extra) {
		    const catalog = extra?.libraryStringCatalog;
		    return Array.isArray(catalog) && catalog.every((value) => typeof value === 'string') ? catalog : undefined;
		}
		function extraWithoutLibraryStringCatalog(extra) {
		    return Object.fromEntries(Object.entries(extra ?? {}).filter(([key]) => key !== 'libraryStringCatalog'));
		}
		function parseOrCadDsnCfb(fileContents, options = {}) {
		    const entries = (0, OrcadDsnCfbCfb_1.readOrCadDsnCfbEntries)(fileContents);
		    const libraryEntries = entries.filter((entry) => entry.path === 'Root Entry/Library' && entry.type === 2);
		    const libraryBytes = libraryEntries.length === 1 ? libraryEntries[0].content : undefined;
		    const decodedLibraryStringCatalog = libraryBytes ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLibraryStringCatalog(libraryBytes) : undefined;
		    const libraryStringCatalog = decodedLibraryStringCatalog ?? libraryStringCatalogFromExtra(options.extra);
		    const pageStreamNames = new Set(entries.filter((entry) => /^Root Entry\/Views\/[^/]+\/Pages\/[^/]+$/.test(entry.path)).map((entry) => entry.path));
		    const roleDiagnostics = [];
		    if (libraryBytes !== undefined && decodedLibraryStringCatalog === undefined) {
		        roleDiagnostics.push({
		            severity: 'warning',
		            code: 'library-string-catalog-withheld',
		            streamPath: 'Root Entry/Library',
		            message: 'CFB Library string catalogue does not form a complete counted-string table',
		        });
		    }
		    const knownSpecialSymbols = collectKnownSpecialCacheSymbolNames(entries, roleDiagnostics);
		    const resolvedOptions = {
		        maxDiagnosticAsciiRunsPerStream: options.maxDiagnosticAsciiRunsPerStream ?? 200,
		        minDiagnosticAsciiRunLength: options.minDiagnosticAsciiRunLength ?? 4,
		        includeDiagnostics: options.includeDiagnostics ?? false,
		        includeRawRecords: options.includeRawRecords ?? false,
		        diagnosticMode: options.diagnosticMode ?? 'summary',
		        extra: {
		            ...extraWithoutLibraryStringCatalog(options.extra),
		            knownSpecialSymbols,
		            roleDiagnostics,
		        },
		    };
		    const streams = entries
		        .map((entry) => (0, OrcadDsnCfbStreamParsing_1.summarizeOrCadDsnCfbStream)(entry, pageStreamNames, resolvedOptions, libraryStringCatalog))
		        .filter((entry) => Boolean(entry));
		    const hierarchyOccurrenceTrust = (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)({ streams });
		    const document = (0, OrcadDsnCfbMaterializer_1.materializeOrCadDsnCfbDocument)(entries, streams, hierarchyOccurrenceTrust);
		    return roleDiagnostics.length > 0 ?
		        { ...document, diagnostics: [...(document.diagnostics ?? []), ...roleDiagnostics] }
		        : document;
		}
		
	} (OrcadDsnCfbParser));
	return OrcadDsnCfbParser;
}

var OrcadCaptureFontMetrics = {};

var hasRequiredOrcadCaptureFontMetrics;

function requireOrcadCaptureFontMetrics () {
	if (hasRequiredOrcadCaptureFontMetrics) return OrcadCaptureFontMetrics;
	hasRequiredOrcadCaptureFontMetrics = 1;
	Object.defineProperty(OrcadCaptureFontMetrics, "__esModule", { value: true });
	OrcadCaptureFontMetrics.OrcadCaptureFontMetrics = void 0;
	var OrcadCaptureFontMetrics$1;
	(function (OrcadCaptureFontMetrics) {
	    let AdvanceMissReason;
	    (function (AdvanceMissReason) {
	        AdvanceMissReason["UnknownFont"] = "unknown_font";
	        AdvanceMissReason["UnsolvedFont"] = "unsolved_font";
	        AdvanceMissReason["UnobservedGlyph"] = "unobserved_glyph";
	        AdvanceMissReason["UnresolvedGlyphPair"] = "unresolved_glyph_pair";
	    })(AdvanceMissReason = OrcadCaptureFontMetrics.AdvanceMissReason || (OrcadCaptureFontMetrics.AdvanceMissReason = {}));
	    const STROKE_HEIGHT_8_GLYPHS = new Map([
	        ['0', 4],
	        ['1', 4],
	        ['2', 4],
	        ['3', 4],
	        ['4', 4],
	        ['5', 4],
	        ['6', 4],
	        ['7', 4],
	        ['8', 4],
	        ['9', 4],
	        ['#', 4],
	        ['+', 4],
	        [',', 2],
	        ['-', 2],
	        ['.', 2],
	        ['A', 5],
	        ['B', 5],
	        ['C', 5],
	        ['D', 5],
	        ['E', 5],
	        ['F', 4],
	        ['G', 5],
	        ['H', 5],
	        ['I', 2],
	        ['K', 5],
	        ['L', 4],
	        ['M', 5],
	        ['N', 5],
	        ['O', 5],
	        ['P', 5],
	        ['Q', 5],
	        ['R', 5],
	        ['S', 5],
	        ['T', 5],
	        ['U', 5],
	        ['V', 5],
	        ['W', 7],
	        ['X', 5],
	        ['^', 3],
	        ['_', 4],
	    ]);
	    const STROKE_HEIGHT_8_TIED_PAIRS = [
	        { first: '[', second: ']', pairAdvance: 4 },
	        { first: 'i', second: 'n', pairAdvance: 6 },
	    ];
	    const STROKE_HEIGHT_MINUS_9_GLYPHS = new Map([
	        ['0', 5],
	        ['1', 5],
	        ['2', 5],
	        ['3', 5],
	        ['4', 5],
	        ['5', 5],
	        ['6', 5],
	        ['7', 5],
	        ['8', 5],
	        ['9', 5],
	        [',', 3],
	        ['B', 6],
	    ]);
	    const STROKE_HEIGHT_8 = { Name: '', Height: 8, Width: 3, Weight: 400, Italic: 0 };
	    const STROKE_HEIGHT_MINUS_9 = { Name: '', Height: -9, Width: 4, Weight: 400, Italic: 0 };
	    const ROWS = new Map([
	        [rowKey(STROKE_HEIGHT_8), { base: 20, glyphs: STROKE_HEIGHT_8_GLYPHS, tiedPairs: STROKE_HEIGHT_8_TIED_PAIRS }],
	        [rowKey(STROKE_HEIGHT_MINUS_9), { glyphs: STROKE_HEIGHT_MINUS_9_GLYPHS, tiedPairs: [] }],
	    ]);
	    const SAMPLED_FAMILIES = new Set([familyKey(STROKE_HEIGHT_8), familyKey(STROKE_HEIGHT_MINUS_9)]);
	    function rowKey(font) {
	        return `${font.Name}\0${font.Height}\0${font.Width}\0${font.Weight}\0${font.Italic}`;
	    }
	    function familyKey(font) {
	        return `${font.Name}\0${font.Height}`;
	    }
	    function tiedGlyphAdvance(text, tiedPairs) {
	        let total = 0;
	        for (const pair of tiedPairs) {
	            let firstCount = 0;
	            let secondCount = 0;
	            for (const glyph of text) {
	                if (glyph === pair.first) {
	                    firstCount += 1;
	                }
	                else if (glyph === pair.second) {
	                    secondCount += 1;
	                }
	            }
	            if (firstCount !== secondCount) {
	                return { unbalancedGlyph: firstCount > secondCount ? pair.first : pair.second };
	            }
	            total += firstCount * pair.pairAdvance;
	        }
	        return { advance: total };
	    }
	    function measure(text, font) {
	        const row = ROWS.get(rowKey(font));
	        if (!row) {
	            return {
	                miss: {
	                    reason: SAMPLED_FAMILIES.has(familyKey(font)) ? AdvanceMissReason.UnsolvedFont : AdvanceMissReason.UnknownFont,
	                },
	            };
	        }
	        if (row.base === undefined) {
	            return { miss: { reason: AdvanceMissReason.UnsolvedFont } };
	        }
	        const tied = tiedGlyphAdvance(text, row.tiedPairs);
	        if (tied.advance === undefined) {
	            return { miss: { reason: AdvanceMissReason.UnresolvedGlyphPair, glyph: tied.unbalancedGlyph } };
	        }
	        let total = row.base + tied.advance;
	        for (const glyph of text) {
	            if (row.tiedPairs.some((pair) => pair.first === glyph || pair.second === glyph)) {
	                continue;
	            }
	            const glyphAdvance = row.glyphs.get(glyph);
	            if (glyphAdvance === undefined) {
	                return { miss: { reason: AdvanceMissReason.UnobservedGlyph, glyph } };
	            }
	            total += glyphAdvance;
	        }
	        return { advance: total };
	    }
	    OrcadCaptureFontMetrics.measure = measure;
	    function advance(text, font) {
	        return measure(text, font).advance;
	    }
	    OrcadCaptureFontMetrics.advance = advance;
	    function describeFont(font) {
	        return `font "${font.Name}" height ${font.Height} width ${font.Width} weight ${font.Weight} italic ${font.Italic}`;
	    }
	    OrcadCaptureFontMetrics.describeFont = describeFont;
	})(OrcadCaptureFontMetrics$1 || (OrcadCaptureFontMetrics.OrcadCaptureFontMetrics = OrcadCaptureFontMetrics$1 = {}));
	
	return OrcadCaptureFontMetrics;
}

var orcadLibraryProvenance = {};

var hasRequiredOrcadLibraryProvenance;

function requireOrcadLibraryProvenance () {
	if (hasRequiredOrcadLibraryProvenance) return orcadLibraryProvenance;
	hasRequiredOrcadLibraryProvenance = 1;
	Object.defineProperty(orcadLibraryProvenance, "__esModule", { value: true });
	orcadLibraryProvenance.isCadenceInstalledLibrary = isCadenceInstalledLibrary;
	orcadLibraryProvenance.isFromCadenceLibrary = isFromCadenceLibrary;
	const CADENCE_INSTALL_DIRECTORY_PATTERN = /^(?:cadence|spb_.+|orcad.*)$/i;
	const ROOTED_PATH_PATTERN = /^(?:[A-Za-z]:[\\/]|[\\/])/;
	function isCadenceInstalledLibrary(sourceLibName) {
	    if (!ROOTED_PATH_PATTERN.test(sourceLibName)) {
	        return false;
	    }
	    const segments = sourceLibName
	        .split(/[\\/]+/)
	        .filter((segment) => segment.length > 0)
	        .map((segment) => segment.toLowerCase());
	    for (let index = 1; index + 2 < segments.length; index++) {
	        if (segments[index] !== 'tools' || segments[index + 1] !== 'capture' || segments[index + 2] !== 'library') {
	            continue;
	        }
	        return CADENCE_INSTALL_DIRECTORY_PATTERN.test(segments[index - 1]);
	    }
	    return false;
	}
	function isFromCadenceLibrary(sourceLibName, libraryName) {
	    if (!isCadenceInstalledLibrary(sourceLibName)) {
	        return false;
	    }
	    const fileName = sourceLibName.split(/[\\/]/).pop() ?? sourceLibName;
	    return fileName.replace(/\.olb$/i, '').toUpperCase() === libraryName;
	}
	
	return orcadLibraryProvenance;
}

var hasRequiredOrcadDsnCfbLogicalSchemaConverter;

function requireOrcadDsnCfbLogicalSchemaConverter () {
	if (hasRequiredOrcadDsnCfbLogicalSchemaConverter) return OrcadDsnCfbLogicalSchemaConverter;
	hasRequiredOrcadDsnCfbLogicalSchemaConverter = 1;
	Object.defineProperty(OrcadDsnCfbLogicalSchemaConverter, "__esModule", { value: true });
	OrcadDsnCfbLogicalSchemaConverter.OrcadDsnCfbLogicalSchemaConverter = void 0;
	OrcadDsnCfbLogicalSchemaConverter.convertOrCadDsnCfbToLogicalSchema = convertOrCadDsnCfbToLogicalSchema;
	OrcadDsnCfbLogicalSchemaConverter.parseOrCadDsnCfbLogicalSchema = parseOrCadDsnCfbLogicalSchema;
	const LogicalSchema_1 = requireLogicalSchema();
	const OrcadDsnCfbParser_1 = requireOrcadDsnCfbParser();
	const OrcadDsnCfbMaterializer_1 = requireOrcadDsnCfbMaterializer();
	const OrcadDsnCfbDeclarativeSpecs_1 = requireOrcadDsnCfbDeclarativeSpecs();
	const OrcadCaptureFontMetrics_1 = requireOrcadCaptureFontMetrics();
	const OrcadDsnCfbTypes_1 = requireOrcadDsnCfbTypes();
	const OrcadDsnCfbOccurrenceOwnership_1 = requireOrcadDsnCfbOccurrenceOwnership();
	const OrcadDsnCfbStreamUtils_1 = requireOrcadDsnCfbStreamUtils();
	const base64_1 = requireBase64$1();
	const portOccurrenceNormalization_1 = requirePortOccurrenceNormalization();
	const orcadLibraryProvenance_1 = requireOrcadLibraryProvenance();
	var OrcadDsnCfbLogicalSchemaConverter$1;
	(function (OrcadDsnCfbLogicalSchemaConverter) {
	    const isCapsymPowerSymbolName = OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.isCapsymPowerSymbolName;
	    (function (DiagnosticSeverity) {
	        DiagnosticSeverity["Info"] = "info";
	        DiagnosticSeverity["Warning"] = "warning";
	        DiagnosticSeverity["Error"] = "error";
	    })(OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity || (OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity = {}));
	    let PhysicalPadRowSource;
	    (function (PhysicalPadRowSource) {
	        PhysicalPadRowSource["CatalogScan"] = "catalog_scan";
	        PhysicalPadRowSource["FramedRecord"] = "framed_record";
	    })(PhysicalPadRowSource || (PhysicalPadRowSource = {}));
	    let PhysicalPadOutcome;
	    (function (PhysicalPadOutcome) {
	        PhysicalPadOutcome["Decoded"] = "decoded";
	        PhysicalPadOutcome["PadsUndecoded"] = "pads_undecoded";
	        PhysicalPadOutcome["NoPins"] = "no_pins";
	    })(PhysicalPadOutcome || (PhysicalPadOutcome = {}));
	    const PHYSICAL_PIN_DIAGNOSTIC_LIMIT_PER_REASON = 25;
	    let MultipartSectionState;
	    (function (MultipartSectionState) {
	        MultipartSectionState["Resolved"] = "resolved";
	        MultipartSectionState["ReferenceWithheld"] = "reference_withheld";
	        MultipartSectionState["NoOccurrenceAnnotation"] = "no_occurrence_annotation";
	        MultipartSectionState["Withheld"] = "withheld";
	    })(MultipartSectionState || (MultipartSectionState = {}));
	    const LAST_FOUR_DIGIT_YEAR_MS = Date.UTC(9999, 11, 31, 23, 59, 59, 999);
	    const DEFAULT_COLOR_VALUE = 48;
	    const DEFAULT_LINE_STYLE = 0;
	    const DEFAULT_LINE_WIDTH = 0;
	    const DEFAULT_NET_LINE_STYLE = 5;
	    const DEFAULT_NET_LINE_WIDTH = 3;
	    const DEFAULT_DISPLAY_TYPE_VALUE = 1;
	    const DEFAULT_PIN_TYPE = 4;
	    const UNNAMED_SYMBOL_PIN = '$PIN0';
	    const PAGE_ALIAS_STRING_MARKER = 0x0003_0000;
	    const POWER_PIN_TYPE = 7;
	    const PinFlag = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbSymbolPinDisplayFlag;
	    const UNNAMED_DISPLAY_PROPERTY_PREFIX = '<unnamed property ';
	    const UNNAMED_DISPLAY_PROPERTY_NAME = (propertyCode) => `${UNNAMED_DISPLAY_PROPERTY_PREFIX}${propertyCode}>`;
	    function emitCatalogFallbackDiagnostics(document, schematics, options) {
	        if (!options.diagnostics) {
	            return;
	        }
	        const hasStringCatalog = document.streams.some((stream) => stream.parsed?.kind === 'library' && (stream.parsed.stringCatalog?.length ?? 0) > 0);
	        if (!hasStringCatalog) {
	            options.diagnostics.push({
	                severity: 'warning',
	                message: 'CFB DSN Library string catalog could not be decoded; no part carries catalog metadata and no display record carries a name',
	            });
	            return;
	        }
	        let partInstsWithoutProperties = 0;
	        for (const schematic of schematics) {
	            for (const page of schematic.Page) {
	                for (const partInst of page.PartInst ?? []) {
	                    if ((partInst.UserProperty ?? []).length === 0) {
	                        partInstsWithoutProperties++;
	                    }
	                }
	            }
	        }
	        let unnamedDisplayProperties = 0;
	        for (const schematic of schematics) {
	            for (const page of schematic.Page) {
	                const converted = [
	                    ...(page.PartInst ?? []).flatMap((part) => part.DisplayProperty ?? []),
	                    ...(page.DrawnInst ?? []).flatMap((drawnInst) => drawnInst.DisplayProperty ?? []),
	                    ...(page.Global ?? []).flatMap((global) => (global.DisplayProperty ? [global.DisplayProperty] : [])),
	                    ...(page.OffPageConnector ?? []).flatMap((connector) => connector.OffPageConnectorDisplayProp ?? []),
	                    ...(page.Port ?? []).flatMap((port) => port.PortDisplayProp ?? []),
	                    ...(page.TitleBlock ?? []).flatMap((titleBlock) => titleBlock.DisplayProperty ?? []),
	                ];
	                unnamedDisplayProperties += converted.filter((property) => property.Name.startsWith(UNNAMED_DISPLAY_PROPERTY_PREFIX)).length;
	            }
	        }
	        if (partInstsWithoutProperties > 0) {
	            options.diagnostics.push({
	                severity: 'warning',
	                message: `CFB DSN materialised no user properties for ${partInstsWithoutProperties} part instance(s); the design's property rows name none for them`,
	            });
	        }
	        if (unnamedDisplayProperties > 0) {
	            options.diagnostics.push({
	                severity: 'warning',
	                message: `CFB DSN Library string catalog names no property for ${unnamedDisplayProperties} display record(s); they are reported under their numeric property code`,
	            });
	        }
	    }
	    function convertDocument(document, options = {}) {
	        const collectedDiagnostics = (options.diagnostics ??= []);
	        for (const diagnostic of document.diagnostics ?? []) {
	            collectedDiagnostics.push({
	                severity: diagnostic.severity,
	                message: `CFB decode [${diagnostic.code}] ${diagnostic.message}`,
	                streamPath: diagnostic.streamPath,
	                offset: diagnostic.offset,
	            });
	        }
	        const pages = getPageStreams(document);
	        const physicalPinDiagnostics = createPhysicalPinDiagnosticState(collectedDiagnostics);
	        const cacheCatalog = buildCacheCatalog(document, physicalPinDiagnostics);
	        const libraryInstanceCatalog = buildLibraryInstanceCatalog(document);
	        const occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document);
	        const recordedRootName = occurrenceTrust.streamVetoes.size === 0 && occurrenceTrust.viewVetoes.size === 0 ?
	            uniqueRecordedRootName(document, document.schematicViews.length > 0 ?
	                document.schematicViews.map((stream) => stream.schematicName ?? stream.viewName)
	                : Array.from(new Set(pages.map((page) => page.parsed.viewName))))
	            : undefined;
	        const libraryStringCatalog = buildLibraryStringCatalog(document);
	        const cisSchematicInfoCatalog = buildCisSchematicInfoCatalog(document);
	        reportUndecodedSymbolPinFlagBits(document, physicalPinDiagnostics);
	        reportUndecodedSymbolPinFields(document, physicalPinDiagnostics);
	        const partValueConnectorGeometryGroups = collectPartValueConnectorGeometryGroups(document);
	        const liveConnectorPins = buildLiveConnectorSymbolPinCatalog(document, physicalPinDiagnostics);
	        const packageViewPinCatalog = buildPackageViewPinCatalog(document, cacheCatalog, physicalPinDiagnostics);
	        const instanceSourceLibraryCatalog = buildInstanceSourceLibraryCatalog(document, physicalPinDiagnostics);
	        const markerSourceLibraryCatalog = buildMarkerSourceLibraryCatalog(document);
	        calibrateRecordedPlacedPorts(document);
	        const physicalPinNumberCatalogs = buildPhysicalPinNumberCatalogs(document, cacheCatalog, physicalPinDiagnostics);
	        const placedPartCacheIdentityCatalog = buildPlacedPartCacheIdentityCatalog(document, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalogs.pinNumbers, instanceSourceLibraryCatalog, markerSourceLibraryCatalog, physicalPinDiagnostics);
	        recoverMissingPlacedPartReferences(document, cacheCatalog, placedPartCacheIdentityCatalog, physicalPinDiagnostics);
	        const sheetSymbolPackageCatalog = buildSheetSymbolPackageCatalog(document, options.sourceFileName, options.diagnostics);
	        const hierarchySheetReferenceCatalog = buildHierarchySheetReferenceCatalog(document, physicalPinDiagnostics, occurrenceTrust);
	        const hierarchyOccurrenceCatalog = buildHierarchyOccurrenceCatalog(document, physicalPinDiagnostics, occurrenceTrust);
	        const partInstanceOccurrenceReferences = new WeakMap();
	        const variantPropertyCatalog = buildVariantPropertyCatalog(document);
	        const pageTextOptions = pageCommentTextOptionsForDocument(document);
	        const defaultFont = font();
	        const convertedCache = buildCache(document, options, cacheCatalog, physicalPinNumberCatalogs.pinNumbers, physicalPinNumberCatalogs.sectionCounts, physicalPinNumberCatalogs.numberingStyles, physicalPinNumberCatalogs.padPresence, physicalPinDiagnostics, placedPartCacheIdentityCatalog, markerSourceLibraryCatalog, liveConnectorPins, partValueConnectorGeometryGroups);
	        const pageCommentTextCount = document.streams.reduce((total, stream) => stream.parsed?.kind === 'page' ?
	            total + (stream.decodedPrimitives ?? []).filter((primitive) => primitive.kind === 'comment-text').length
	            : total, 0);
	        if (pageCommentTextCount > 0 && !pageTextOptions.defaultFontName) {
	            emitBudgetedDiagnostic(physicalPinDiagnostics, '', 'comment-text-font-unnamed', `no design default names a text face, so ${pageCommentTextCount} page comment text record(s) carry no font name`, 'CFB text metric', 'info');
	        }
	        reportUndecodedPinLabelVisibility((convertedCache.Package ?? []).reduce((total, pkg) => total + (pkg.LibPart ?? []).reduce((count, libPart) => count + (libPart.NormalView?.length ?? 0), 0), 0), physicalPinDiagnostics);
	        const connectorSymbolGeometryCatalog = buildConnectorSymbolGeometryCatalog(document, physicalPinDiagnostics, partValueConnectorGeometryGroups, convertedCache, liveConnectorPins);
	        populatePlacedSymbolSourceLibraries(document, placedPartCacheIdentityCatalog, markerSourceLibraryCatalog, physicalPinDiagnostics);
	        const multipartSectionSuffixes = buildMultipartSectionSuffixLookup(document, placedPartCacheIdentityCatalog, physicalPinNumberCatalogs.sectionCounts, physicalPinDiagnostics, occurrenceTrust);
	        const schematicViewNames = new WeakMap();
	        const schematics = buildSchematics(document, pages, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraryCatalog, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPinNumberCatalogs.padPresence, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics, occurrenceTrust, recordedRootName, schematicViewNames);
	        const storedIntersheetReferences = intersheetReferenceValues(document, options.diagnostics);
	        applyIntersheetReferenceValues(schematics, storedIntersheetReferences, pageStreamPathsByPageName(pages), options.diagnostics);
	        const rootName = selectRootSchematicName(document, schematics, physicalPinDiagnostics, occurrenceTrust, recordedRootName);
	        const maxEpoch = maxPageModifyEpoch(pages);
	        emitCatalogFallbackDiagnostics(document, schematics, options);
	        finalizePhysicalPinDiagnostics(physicalPinDiagnostics);
	        return {
	            DesignExportVersion: 17,
	            Name: options.sourceFileName ?? findDesignName(document) ?? rootName,
	            ObjectType: 'SCHEMATIC',
	            RootName: rootName,
	            UseExplicitDbId: true,
	            DateTime: dateTimeFromEpoch(maxEpoch),
	            DefaultValues: defaultValues(defaultFont),
	            Cache: convertedCache,
	            Netgroup: { Bundle: [] },
	            Schematic: schematics,
	            Occurrences: buildOccurrences(schematics, rootName, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, buildOccurrenceNetSystemIdCatalog(document, physicalPinDiagnostics, occurrenceTrust), schematicViewNames, physicalPinDiagnostics),
	            Cis: {
	                Groups: {},
	                BomVariants: {},
	            },
	            IntersheetReferenceValues: Array.from(new Set(storedIntersheetReferences.values())),
	        };
	    }
	    OrcadDsnCfbLogicalSchemaConverter.convertDocument = convertDocument;
	    const IntersheetReferenceValuePattern = /^(\[Grid.+\]|\d+[!^]?(,\d+[!^]?)*)$/;
	    const IntersheetReferenceUserPropertyName = 'IREF';
	    const OrCadStoredIntersheetReferenceConflictDiagnosticType = 'iref-stored-conflict';
	    const StoredIntersheetReferenceDbIdByteLength = 4;
	    const StoredIntersheetReferenceIndexBackOffset = 20;
	    const StoredIntersheetReferenceIndexLead = 12;
	    const IntersheetReferenceRoles = new Set([
	        'port',
	        'off-page-connector',
	    ]);
	    function storedIntersheetReferenceKey(pageStreamPath, role, dbId) {
	        return JSON.stringify([pageStreamPath, role, dbId]);
	    }
	    function pageStreamLookupKey(viewName, pageName) {
	        return JSON.stringify([viewName, pageName]);
	    }
	    function pageStreamPathsByPageName(pages) {
	        const paths = new Map();
	        const ambiguous = new Set();
	        for (const page of pages) {
	            const key = pageStreamLookupKey(page.parsed.viewName, page.parsed.pageName);
	            const existing = paths.get(key);
	            if (existing !== undefined && existing !== page.stream.path) {
	                ambiguous.add(key);
	                continue;
	            }
	            paths.set(key, page.stream.path);
	        }
	        for (const key of ambiguous) {
	            paths.delete(key);
	        }
	        return paths;
	    }
	    function libraryStringCatalog(document) {
	        const catalog = [];
	        for (const stream of document.streams) {
	            if (stream.parsed?.kind !== 'library') {
	                continue;
	            }
	            for (const value of stream.parsed.stringCatalog ?? []) {
	                catalog.push(value);
	            }
	        }
	        return catalog;
	    }
	    function storedIntersheetReferenceIndexes(record, strings) {
	        const recordEnd = record.offset + record.byteLength;
	        const earliestIndexOffset = Math.max(0, record.offset - StoredIntersheetReferenceIndexLead);
	        const indexes = [];
	        for (const string of strings) {
	            if (string.offset >= recordEnd) {
	                break;
	            }
	            const valueIndex = string.precedingInt32Le20;
	            if (valueIndex === undefined || string.offset < record.offset || string.value !== record.symbolName) {
	                continue;
	            }
	            if (string.followingUInt32Le !== record.dbId) {
	                continue;
	            }
	            if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string) + StoredIntersheetReferenceDbIdByteLength >
	                recordEnd) {
	                continue;
	            }
	            if (string.offset - StoredIntersheetReferenceIndexBackOffset < earliestIndexOffset) {
	                continue;
	            }
	            indexes.push(valueIndex);
	        }
	        return indexes;
	    }
	    function intersheetReferenceValues(document, diagnostics) {
	        const catalog = libraryStringCatalog(document);
	        const values = new Map();
	        const conflicting = new Set();
	        const conflictingConnectors = new Set();
	        let outOfRangeIndexes = 0;
	        let unshapedValues = 0;
	        for (const stream of document.streams) {
	            const records = stream.decodedPageObjects;
	            if (!records) {
	                continue;
	            }
	            const strings = [...(stream.lengthPrefixedStrings ?? [])].sort((left, right) => left.offset - right.offset);
	            for (const record of records) {
	                if (record.kind !== 'placed-symbol' || !IntersheetReferenceRoles.has(record.role)) {
	                    continue;
	                }
	                const key = storedIntersheetReferenceKey(stream.path, record.role, record.dbId);
	                for (const valueIndex of storedIntersheetReferenceIndexes(record, strings)) {
	                    if (valueIndex < 0 || valueIndex >= catalog.length) {
	                        outOfRangeIndexes++;
	                        conflicting.add(key);
	                        continue;
	                    }
	                    const value = catalog[valueIndex];
	                    if (value.trim().length === 0) {
	                        continue;
	                    }
	                    if (!IntersheetReferenceValuePattern.test(value)) {
	                        unshapedValues++;
	                        conflicting.add(key);
	                        continue;
	                    }
	                    const existing = values.get(key);
	                    if (existing !== undefined && existing !== value) {
	                        conflictingConnectors.add(key);
	                        conflicting.add(key);
	                        continue;
	                    }
	                    values.set(key, value);
	                }
	            }
	        }
	        for (const key of conflicting) {
	            values.delete(key);
	        }
	        if (diagnostics) {
	            if (outOfRangeIndexes > 0) {
	                diagnostics.push({
	                    type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
	                    severity: 'warning',
	                    message: `${outOfRangeIndexes} connector placement record reading(s) point outside the Library string catalog, so those connectors carry no stored intersheet reference`,
	                });
	            }
	            if (unshapedValues > 0) {
	                diagnostics.push({
	                    type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
	                    severity: 'warning',
	                    message: `${unshapedValues} connector placement record reading(s) point at a Library string that is not an intersheet-reference value, so those connectors carry no stored value`,
	                });
	            }
	            if (conflictingConnectors.size > 0) {
	                diagnostics.push({
	                    type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
	                    severity: 'warning',
	                    message: `${conflictingConnectors.size} connector(s) are given two different intersheet-reference values by their placement records, so they carry none`,
	                });
	            }
	        }
	        return values;
	    }
	    function canonicalIntersheetReferenceValue(value) {
	        const collapsed = value.trim().replace(/\s+/g, ' ');
	        if (!/^\d/.test(collapsed)) {
	            return collapsed;
	        }
	        const pages = collapsed
	            .split(',')
	            .map((token) => token.trim())
	            .filter((token) => token.length > 0);
	        return Array.from(new Set(pages)).sort().join(',');
	    }
	    function applyIntersheetReferenceValues(schematics, values, pageStreamPaths, diagnostics) {
	        let conflicts = 0;
	        let unmappedPages = 0;
	        const reconcile = (key, userProperties) => {
	            const value = key === undefined ? undefined : values.get(key);
	            if (value === undefined) {
	                return userProperties;
	            }
	            const present = userProperties ?? [];
	            const existing = present.filter((property) => property.Name === IntersheetReferenceUserPropertyName);
	            if (existing.length === 0) {
	                return [...present, { Name: IntersheetReferenceUserPropertyName, Value: value }];
	            }
	            const canonical = canonicalIntersheetReferenceValue(value);
	            if (existing.some((property) => canonicalIntersheetReferenceValue(property.Value) !== canonical)) {
	                conflicts++;
	                return present.filter((property) => property.Name !== IntersheetReferenceUserPropertyName);
	            }
	            let kept = false;
	            return present.filter((property) => {
	                if (property.Name !== IntersheetReferenceUserPropertyName) {
	                    return true;
	                }
	                const first = !kept;
	                kept = true;
	                return first;
	            });
	        };
	        for (const schematic of schematics) {
	            for (const page of schematic.Page) {
	                const streamPath = pageStreamPaths.get(pageStreamLookupKey(schematic.Name, page.Name));
	                if (streamPath === undefined) {
	                    unmappedPages++;
	                    continue;
	                }
	                for (const port of page.Port ?? []) {
	                    port.PortUserProp = reconcile(storedIntersheetReferenceKey(streamPath, 'port', port.DbId), port.PortUserProp);
	                }
	                for (const connector of page.OffPageConnector ?? []) {
	                    connector.OffPageConnectorUserProp = reconcile(storedIntersheetReferenceKey(streamPath, 'off-page-connector', connector.DbId), connector.OffPageConnectorUserProp);
	                }
	            }
	        }
	        if (diagnostics && conflicts > 0) {
	            diagnostics.push({
	                type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
	                severity: 'warning',
	                message: `${conflicts} connector(s) spell one intersheet reference in their own page record and another in the Library string catalog, so they carry none`,
	            });
	        }
	        if (diagnostics && unmappedPages > 0) {
	            diagnostics.push({
	                type: OrCadStoredIntersheetReferenceConflictDiagnosticType,
	                severity: 'warning',
	                message: `${unmappedPages} page(s) share a view and page name with another page stream, so their connectors carry no stored intersheet reference`,
	            });
	        }
	    }
	    function nearMissSchematicViewKey(name) {
	        return name.replace(/[\s_-]+/g, '').toUpperCase();
	    }
	    function hierarchyClaimedViewKeys(document, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
	        const keys = new Set();
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'hierarchy') {
	                continue;
	            }
	            for (const entry of parsed.entries) {
	                if (!entry.contentsViewName) {
	                    continue;
	                }
	                if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, entry.contentsViewName, entry.refInstId ?? entry.dbId, 'root-selection')) {
	                    continue;
	                }
	                if (entry.contentsViewName === parsed.viewName) {
	                    if (entry.dbId !== undefined) {
	                        emitBudgetedDiagnostic(diagnostics, parsed.viewName, 'hierarchy-view-draws-itself', `view "${parsed.viewName}" carries a sheet-symbol instance whose child is that same view, which the parse does not expand`, 'CFB design', 'warning');
	                    }
	                    continue;
	                }
	                keys.add(entry.contentsViewName);
	            }
	        }
	        return keys;
	    }
	    function reportSchematicViewNearMisses(unclaimed, viewFolderByExportedName, childViewNames, diagnostics) {
	        const claimedNearKeys = new Map();
	        for (const claimed of childViewNames) {
	            claimedNearKeys.set(nearMissSchematicViewKey(claimed), claimed);
	        }
	        for (const schematic of unclaimed) {
	            const viewFolder = viewFolderByExportedName.get(schematic.Name) ?? schematic.Name;
	            const near = claimedNearKeys.get(nearMissSchematicViewKey(schematic.Name)) ??
	                claimedNearKeys.get(nearMissSchematicViewKey(viewFolder));
	            if (near === undefined) {
	                continue;
	            }
	            emitBudgetedDiagnostic(diagnostics, schematic.Name, 'schematic-view-name-near-miss', `no hierarchy row names schematic "${schematic.Name}", and one names "${near}", which differs only in case or separators`, 'CFB design', 'warning');
	        }
	    }
	    function selectRootSchematicName(document, schematics, diagnostics, occurrenceTrust, recordedRootName) {
	        const childViewNames = hierarchyClaimedViewKeys(document, diagnostics, occurrenceTrust);
	        if (occurrenceTrust.streamVetoes.size > 0 || occurrenceTrust.viewVetoes.size > 0) {
	            emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', 'root-selection: incomplete hierarchy claims cannot establish an unclaimed root', 'CFB hierarchy', 'warning');
	            return '';
	        }
	        const viewFolderByExportedName = new Map();
	        for (const stream of document.schematicViews) {
	            viewFolderByExportedName.set(stream.schematicName ?? stream.viewName, stream.viewName);
	        }
	        const unclaimed = schematics.filter((schematic) => {
	            const viewFolder = viewFolderByExportedName.get(schematic.Name) ?? schematic.Name;
	            return !childViewNames.has(schematic.Name) && !childViewNames.has(viewFolder);
	        });
	        reportSchematicViewNearMisses(unclaimed, viewFolderByExportedName, childViewNames, diagnostics);
	        if (recordedRootName !== undefined) {
	            return recordedRootName;
	        }
	        if (unclaimed.length === 1) {
	            return unclaimed[0].Name;
	        }
	        if (schematics.length > 0) {
	            const candidates = unclaimed.map((schematic) => `"${schematic.Name}"`).join(', ');
	            emitBudgetedDiagnostic(diagnostics, 'design', 'root-schematic-ambiguous', `schematics=${schematics.length} unclaimedByHierarchy=${unclaimed.length}${candidates === '' ? '' : ` (${candidates})`}; the design names no single root`, 'CFB design', 'warning');
	        }
	        return '';
	    }
	    function parseLogicalSchema(fileContents, options = {}) {
	        return convertDocument((0, OrcadDsnCfbParser_1.parseOrCadDsnCfb)(fileContents, options), options);
	    }
	    OrcadDsnCfbLogicalSchemaConverter.parseLogicalSchema = parseLogicalSchema;
	    OrcadDsnCfbLogicalSchemaConverter.__testing = {
	        hierarchyClaimedViewKeys,
	        buildOccurrenceNetSystemIdCatalog,
	        buildMultipartSectionSuffixLookup,
	        buildHierarchyReferenceLookup,
	        buildHierarchyOccurrenceCatalog,
	        sectionIndexMatchesDesignator,
	        arcExtentPoints,
	        decodedViewSuffix,
	        portSymbolBoundingBox,
	        preferredProjectModuleDsnPath,
	        createPhysicalPinDiagnosticState,
	        referencePrefixOfDesignator,
	    };
	    function getPageStreams(document) {
	        const pages = [];
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind === 'page') {
	                pages.push({ stream, parsed, index: pages.length });
	            }
	        }
	        return pages;
	    }
	    function buildVariantPropertyCatalog(document) {
	        const catalog = new Map();
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'variant-text') {
	                continue;
	            }
	            for (const row of parsed.rows) {
	                const properties = variantUserPropertiesFromRow(row.properties ?? []);
	                const byName = new Map(properties.map((property) => [property.Name, property.Value]));
	                const partNumber = byName.get('Part_Number');
	                const value = byName.get('Value');
	                if (!partNumber || !value) {
	                    continue;
	                }
	                catalog.set(variantPropertyKey(partNumber, value), properties);
	            }
	        }
	        return catalog;
	    }
	    function variantUserPropertiesFromRow(properties) {
	        const result = [];
	        for (const property of properties) {
	            const name = variantPropertyName(property.name);
	            if (!name || property.value.length === 0 || property.value === 'UNDEFINED') {
	                continue;
	            }
	            result.push({ Name: name, Value: property.value });
	        }
	        return result;
	    }
	    function variantPropertyName(name) {
	        switch (name) {
	            case 'Part Number':
	                return 'Part_Number';
	            case 'Part type':
	                return 'Part_Type';
	            case 'Value':
	            case 'Description':
	            case 'Tolerance':
	            case 'Implementation':
	            case 'PCB Footprint':
	                return name;
	            default:
	                return undefined;
	        }
	    }
	    function variantPropertyKey(partNumber, value) {
	        return `${partNumber.toUpperCase()}|${normalizeCatalogValue(value).toUpperCase()}`;
	    }
	    function buildSchematics(document, pages, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraries, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics, occurrenceTrust, recordedRootName, schematicViewNames) {
	        const pageGroups = new Map();
	        for (const page of pages) {
	            const group = pageGroups.get(page.parsed.viewName) ?? [];
	            group.push(page);
	            pageGroups.set(page.parsed.viewName, group);
	        }
	        const schematicStreams = sortSchematicStreams(document.schematicViews.length > 0 ? document.schematicViews : synthesizeSchematicStreams(pageGroups), hierarchyClaimedViewKeys(document, physicalPinDiagnostics, occurrenceTrust), recordedRootName);
	        return schematicStreams.map((schematicStream) => {
	            const schematicPages = (pageGroups.get(schematicStream.viewName) ?? [])
	                .map((page) => buildPage(page, pages.length, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraries, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics))
	                .sort(compareSchematicPagesByXmlOrder);
	            const schematic = {
	                Name: schematicStream.schematicName ?? schematicStream.viewName,
	                StoredPhysicalNetNames: rootHierarchyPhysicalNetNames(document, schematicStream.viewName, physicalPinDiagnostics),
	                MaxDbId: maxDbId(schematicPages),
	                DateTime: dateTimeFromEpoch(maxPageModifyEpoch(pageGroups.get(schematicStream.viewName) ?? [])),
	                Page: schematicPages,
	            };
	            schematicViewNames.set(schematic, schematicStream.viewName);
	            return schematic;
	        });
	    }
	    function compareSchematicPagesByXmlOrder(left, right) {
	        const leftOrdinal = pageOrdinalFromName(left.Name);
	        const rightOrdinal = pageOrdinalFromName(right.Name);
	        if (leftOrdinal !== undefined && rightOrdinal !== undefined && leftOrdinal !== rightOrdinal) {
	            return leftOrdinal - rightOrdinal;
	        }
	        if (leftOrdinal !== undefined && rightOrdinal === undefined) {
	            return -1;
	        }
	        if (leftOrdinal === undefined && rightOrdinal !== undefined) {
	            return 1;
	        }
	        return left.Name.localeCompare(right.Name);
	    }
	    function pageOrdinalFromName(name) {
	        const match = /^(?:PAGE[_\s-]*)?0*(\d+)(?:\\b|[_\s-])/i.exec(name);
	        if (!match) {
	            return undefined;
	        }
	        const ordinal = Number(match[1]);
	        return Number.isFinite(ordinal) ? ordinal : undefined;
	    }
	    function sortSchematicStreams(streams, claimedViewKeys, recordedRootName) {
	        const isRoot = (stream) => !claimedViewKeys.has(stream.viewName) && !claimedViewKeys.has(stream.schematicName ?? stream.viewName);
	        return [...streams].sort((left, right) => {
	            const leftIsRecordedRoot = (left.schematicName ?? left.viewName) === recordedRootName;
	            const rightIsRecordedRoot = (right.schematicName ?? right.viewName) === recordedRootName;
	            if (leftIsRecordedRoot !== rightIsRecordedRoot) {
	                return leftIsRecordedRoot ? -1 : 1;
	            }
	            const leftIsRoot = isRoot(left);
	            const rightIsRoot = isRoot(right);
	            if (leftIsRoot !== rightIsRoot) {
	                return leftIsRoot ? -1 : 1;
	            }
	            return 0;
	        });
	    }
	    function uniqueRecordedRootName(document, candidateNames) {
	        const recordedRootNames = document.streams.flatMap((stream) => stream.parsed?.kind === 'library' && stream.parsed.recordedRootName !== undefined ?
	            [stream.parsed.recordedRootName]
	            : []);
	        if (recordedRootNames.length !== 1) {
	            return undefined;
	        }
	        const recordedRootName = recordedRootNames[0];
	        return candidateNames.filter((name) => name === recordedRootName).length === 1 ? recordedRootName : undefined;
	    }
	    function synthesizeSchematicStreams(pageGroups) {
	        return Array.from(pageGroups, ([viewName, group]) => ({
	            kind: 'schematic',
	            viewName,
	            schematicName: viewName,
	            pageCountHint: group.length,
	            pageNames: group.map((page) => page.parsed.pageName),
	        }));
	    }
	    function buildPage(page, pageCount, options, pageTextOptions, defaultFont, cacheCatalog, markerSourceLibraries, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog, connectorSymbolGeometryCatalog, placedPartCacheIdentityCatalog, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, variantPropertyCatalog, multipartSectionSuffixes, physicalPinDiagnostics) {
	        const pageSizeInfo = pageSizeInfoFromCfb(page.parsed.pageSizeInfo);
	        const pageObjects = page.stream.decodedPageObjects ?? [];
	        const primitives = page.stream.decodedPrimitives ?? [];
	        const pageObjectNetNames = buildPageObjectNetNameLookup(pageObjects, page.stream.lengthPrefixedStrings, connectorSymbolGeometryCatalog, physicalPinDiagnostics);
	        fillMissingPlacedConnectorNetNames(pageObjects, pageObjectNetNames, page.stream.lengthPrefixedStrings, connectorSymbolGeometryCatalog, physicalPinDiagnostics);
	        const titleBlocks = pageObjects
	            .filter(isPlacedSymbolRole('titleblock'))
	            .map((object) => buildTitleBlock(object, pageCount));
	        if (titleBlocks.length > 0 && storedPageNumber(titleBlocks) === undefined) {
	            emitBudgetedDiagnostic(physicalPinDiagnostics, page.parsed.pageName, 'title-block-page-number-unresolved', `page ${page.parsed.pageName} stores no single title-block page number`, 'CFB page numbering', 'warning');
	        }
	        const globals = pageObjects
	            .filter((object) => isPlacedSymbolRole('global')(object))
	            .map((object) => buildGlobal(object, pageObjectNetNames, physicalPinDiagnostics))
	            .sort(compareNamedPageObjects);
	        const offPageConnectors = pageObjects
	            .filter(isPlacedSymbolRole('off-page-connector'))
	            .map((object) => buildOffPageConnector(object, pageObjectNetNames, pageObjects, physicalPinDiagnostics));
	        const ports = pageObjects
	            .filter(isLogicalPagePort)
	            .map((object) => buildPort(object, pageObjectNetNames, physicalPinDiagnostics));
	        const pageReferenceLookup = buildPageReferenceLookup(page.stream.lengthPrefixedStrings, pageObjects, cacheCatalog, placedPartCacheIdentityCatalog, physicalPinDiagnostics, page.parsed.pageName);
	        const placedParts = pageObjects.filter(isPlacedSymbolRole('part-inst')).sort(comparePageObjectDbId);
	        const placedLibraryRecordNames = new Map();
	        const placedLibraryRecordNameCounts = new Map();
	        for (const object of placedParts) {
	            const packageName = object.resolvedMetadata?.pkgName ?? stripNormalViewSuffix(object.graphicName);
	            const recordName = libraryEntryForPlacedPackage(libraryInstanceCatalog, object.dbId, packageName)?.record.name;
	            if (recordName) {
	                placedLibraryRecordNames.set(object.dbId, recordName);
	                placedLibraryRecordNameCounts.set(recordName, (placedLibraryRecordNameCounts.get(recordName) ?? 0) + 1);
	            }
	        }
	        const partInsts = placedParts.map((object) => buildPartInst(object, libraryInstanceCatalog, libraryStringCatalog, cisSchematicInfoCatalog.get(cisSchematicInfoKey(page.parsed.viewName, page.parsed.pageName)), placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, multipartSectionSuffixes, pageReferenceLookup, variantPropertyCatalog, physicalPinDiagnostics, (placedLibraryRecordNameCounts.get(placedLibraryRecordNames.get(object.dbId) ?? '') ?? 0) > 1, page.parsed.viewName));
	        applySheetReferenceDisplayOverrides(partInsts, page.stream.lengthPrefixedStrings, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences);
	        enrichDrawnInstPortsFromPageConnectivity(pageObjects, physicalPinDiagnostics, page.stream.lengthPrefixedStrings);
	        enrichDrawnInstReferences(pageObjects, physicalPinDiagnostics, page.parsed.pageName);
	        const drawnInstPortDbIds = buildDrawnInstPortDbIdLookup(pageObjects, physicalPinDiagnostics);
	        const drawnInsts = pageObjects
	            .filter(isDrawnInst)
	            .sort(comparePageObjectDbId)
	            .map((object) => buildDrawnInst(object, libraryStringCatalog, physicalPinDiagnostics, drawnInstPortDbIds.get(object)));
	        const busEntries = buildBusEntries(pageObjects);
	        const primitiveGraphics = buildPagePrimitiveGraphics(primitives, pageTextOptions);
	        const nets = buildPageNets(page.stream, options, defaultFont, physicalPinDiagnostics);
	        return {
	            Name: page.parsed.pageName,
	            TitleBlockDisplayed: true,
	            BorderDisplayed: true,
	            GridRefDisplayed: true,
	            PageSizeInfo: pageSizeInfo,
	            PageSize: page.parsed.pageSize ?? `${pageSizeInfo.Width}x${pageSizeInfo.Height}`,
	            OuterBorderMargin: { X: 0, Y: 0 },
	            OuterLineStyle: DEFAULT_LINE_STYLE,
	            OuterLineWidth: DEFAULT_LINE_WIDTH,
	            OuterBorderIsVisible: true,
	            InnerBorderMargin: { X: 0, Y: 0 },
	            InnerLineStyle: DEFAULT_LINE_STYLE,
	            InnerLineWidth: DEFAULT_LINE_WIDTH,
	            InnerLineVisible: true,
	            Color: color(),
	            Font: font(),
	            LabelColor: color(),
	            HorizontalCount: 2,
	            HorizontalWidth: 10,
	            HorizontalChar: 0,
	            HorizontalVisible: true,
	            HorizontalAscending: false,
	            HorizontalStyle: DEFAULT_LINE_STYLE,
	            HorizontalSeparatorWidth: DEFAULT_LINE_WIDTH,
	            VerticalCount: 2,
	            VerticalWidth: 6,
	            VerticalChar: 1,
	            VerticalVisible: true,
	            VerticalAscending: false,
	            VerticalStyle: DEFAULT_LINE_STYLE,
	            VerticalSeparatorWidth: DEFAULT_LINE_WIDTH,
	            PageDateTime: pageDateTime(page.parsed, physicalPinDiagnostics),
	            ...(titleBlocks.length > 0 ? { TitleBlock: titleBlocks } : {}),
	            ...(nets.NetScalar.length > 0 ? { NetScalar: nets.NetScalar } : {}),
	            ...(nets.NetBus.length > 0 ? { NetBus: nets.NetBus } : {}),
	            ...(busEntries.length > 0 ? { BusEntry: busEntries } : {}),
	            ...(globals.length > 0 ? { Global: globals } : {}),
	            ...(partInsts.length > 0 ? { PartInst: partInsts } : {}),
	            ...(drawnInsts.length > 0 ? { DrawnInst: drawnInsts } : {}),
	            ...(ports.length > 0 ? { Port: ports } : {}),
	            ...(offPageConnectors.length > 0 ? { OffPageConnector: offPageConnectors } : {}),
	            ...primitiveGraphics,
	        };
	    }
	    function buildCache(document, options, cacheCatalog, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPadPresenceCatalog, physicalPinDiagnostics, placedPartCacheIdentityCatalog, markerSourceLibraries, liveConnectorPins, partValueConnectorGeometryGroups) {
	        const packageStreams = document.streams.filter((stream) => stream.parsed?.kind === 'package');
	        const symbolStreams = document.streams.filter((stream) => stream.parsed?.kind === 'symbol');
	        const pageSymbolRoles = collectPageSymbolRoles(document);
	        const titleBlockSymbol = buildTitleBlockSymbol(document, pageSymbolRoles);
	        const cache = {};
	        const cacheObjectGroups = groupRootCacheObjects(document, physicalPinDiagnostics);
	        const placedViewEvidence = buildPlacedViewEvidenceCatalog(document, placedPartCacheIdentityCatalog);
	        const livePackages = packageStreams.map((stream) => ({
	            pkg: buildPackage(stream, physicalPinDiagnostics, placedViewEvidence),
	            hasDecodedPhysicalPart: (stream.decodedSymbolObjects?.filter(isPinNumberMap).length ?? 0) > 0,
	        }));
	        const rootCachePackages = buildRootCachePackages(cacheObjectGroups, cacheCatalog, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence);
	        const cachePackageNames = new Set(rootCachePackages.map(({ pkg }) => crossSourcePackageKey(pkg.Name)));
	        const cacheLibrariesByPackageName = new Map();
	        for (const { pkg } of rootCachePackages) {
	            const key = crossSourcePackageKey(pkg.Name);
	            const libraries = cacheLibrariesByPackageName.get(key) ?? new Set();
	            libraries.add(canonicalSourceLibraryName(pkg.SourceLibName));
	            cacheLibrariesByPackageName.set(key, libraries);
	        }
	        const decodedLiveSectionsByPackage = new Map();
	        for (const { pkg, hasDecodedPhysicalPart } of livePackages) {
	            if (!hasDecodedPhysicalPart) {
	                continue;
	            }
	            const sections = decodedLiveSectionsByPackage.get(crossSourcePackageKey(pkg.Name)) ?? new Set();
	            for (const libPart of pkg.LibPart) {
	                sections.add(stripNormalViewSuffix(libPart.CellName ?? pkg.Name));
	            }
	            decodedLiveSectionsByPackage.set(crossSourcePackageKey(pkg.Name), sections);
	        }
	        const packages = [
	            ...livePackages.flatMap(({ pkg, hasDecodedPhysicalPart }) => {
	                if (hasDecodedPhysicalPart || !cachePackageNames.has(crossSourcePackageKey(pkg.Name))) {
	                    return [pkg];
	                }
	                emitPhysicalPinDiagnostic(physicalPinDiagnostics, pkg.Name, 'duplicate-live-package', `name="${pkg.Name}" src="${pkg.SourceLibName}"`, pkg.SourceLibName);
	                return [];
	            }),
	            ...rootCachePackages.flatMap(({ pkg, fallbackCellNames, markedSectionNumberingStyle }) => {
	                const decodedLiveSections = decodedLiveSectionsByPackage.get(crossSourcePackageKey(pkg.Name));
	                if (!decodedLiveSections) {
	                    return [pkg];
	                }
	                const definingLibraries = cacheLibrariesByPackageName.get(crossSourcePackageKey(pkg.Name));
	                if (definingLibraries && definingLibraries.size > 1) {
	                    emitPhysicalPinDiagnostic(physicalPinDiagnostics, pkg.Name, 'live-package-library-ambiguous', `name="${pkg.Name}" src="${pkg.SourceLibName}" libraries=${definingLibraries.size}`, pkg.SourceLibName);
	                    return [pkg];
	                }
	                const keptLibParts = pkg.LibPart.filter((libPart) => {
	                    const cellName = stripNormalViewSuffix(libPart.CellName ?? pkg.Name);
	                    return !(fallbackCellNames.has(cellName) && decodedLiveSections.has(cellName));
	                });
	                if (keptLibParts.length === pkg.LibPart.length) {
	                    return [pkg];
	                }
	                emitPhysicalPinDiagnostic(physicalPinDiagnostics, pkg.Name, 'duplicate-fallback-cache-package', `name="${pkg.Name}" src="${pkg.SourceLibName}" dropped=${pkg.LibPart.length - keptLibParts.length} kept=${keptLibParts.length}`, pkg.SourceLibName);
	                if (keptLibParts.length === 0) {
	                    return [];
	                }
	                return [
	                    {
	                        ...pkg,
	                        AlphabeticNumbering: markedSectionNumberingStyle ?? (keptLibParts.length > 1 ? 1 : 2),
	                        IsHomogeneous: keptLibParts.length <= 1,
	                        LibPart: keptLibParts,
	                    },
	                ];
	            }),
	        ];
	        for (const pkg of packages) {
	            if (!/\.DSN$/i.test(pkg.SourceLibName)) {
	                continue;
	            }
	            for (const libPart of pkg.LibPart) {
	                const pins = libPart.NormalView?.[0]?.PinScalar ?? [];
	                if ((!libPart.PhysicalPart || libPart.PhysicalPart.length === 0) &&
	                    pins.length === 3 &&
	                    pins.every((pin) => isNumericString(pin.Name))) {
	                    libPart.PhysicalPart = [{ PinNumber: pins.map((pin) => ({ Position: pin.Position, Number: pin.Name })) }];
	                    emitPhysicalPinDiagnostic(physicalPinDiagnostics, libPart.CellName ?? pkg.Name, 'dsn-three-pin-physical-part-synthesized', `cell="${libPart.CellName}" source="${pkg.SourceLibName}"`, pkg.SourceLibName, 'info');
	                }
	            }
	        }
	        const placedSourcePackages = buildPlacedSourcePackages(document, rootCachePackages.map(({ pkg }) => pkg), placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, physicalPinDiagnostics);
	        const placedSourcePackageIdentities = new Set(placedSourcePackages.map((pkg) => packageSourceKey(pkg.Name, pkg.SourceLibName)));
	        packages.splice(0, packages.length, ...packages.filter((pkg) => !placedSourcePackageIdentities.has(packageSourceKey(pkg.Name, pkg.SourceLibName))), ...placedSourcePackages);
	        const packageIdentities = new Set(packages.map((pkg) => packageSourceKey(pkg.Name, pkg.SourceLibName)));
	        const placedPackages = buildLivePartPackages(document, packageIdentities, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, physicalPinDiagnostics);
	        const placedPackageIdentities = new Set(placedPackages.map((pkg) => packageSourceKey(pkg.Name, pkg.SourceLibName)));
	        packages.splice(0, packages.length, ...packages.filter((pkg) => !placedPackageIdentities.has(packageSourceKey(pkg.Name, pkg.SourceLibName))), ...placedPackages);
	        diagnoseMissingSourcePackageBodies(document, packages, physicalPinDiagnostics);
	        if (packages.length > 0) {
	            cache.Package = packages;
	        }
	        const portSymbols = [];
	        const offPageSymbols = [];
	        const globalSymbols = [];
	        const ercSymbols = [];
	        for (const stream of symbolStreams) {
	            const name = stream.parsed.symbolName;
	            if (isPortSymbolName(name, pageSymbolRoles)) {
	                portSymbols.push(buildPortSymbol(stream));
	            }
	            else if (isOffPageSymbolName(name)) {
	                offPageSymbols.push(buildOffPageSymbol(stream));
	            }
	            else if (isErcSymbolName(name)) {
	                ercSymbols.push(buildErcSymbol(stream));
	            }
	            else if (pageSymbolRoles.get(name) === 'global' || isCapsymPowerSymbolName(name)) {
	                globalSymbols.push(buildGlobalSymbol(stream, liveConnectorPins, physicalPinDiagnostics));
	            }
	            else if (pageSymbolRoles.has(name)) {
	                globalSymbols.push(buildGlobalSymbol(stream, liveConnectorPins, physicalPinDiagnostics));
	            }
	        }
	        addRootCacheConnectorSymbols([...cacheObjectGroups, ...partValueConnectorGeometryGroups], document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, ercSymbols, liveConnectorPins, physicalPinDiagnostics);
	        addMissingLiveConnectorSymbols(document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, liveConnectorPins, physicalPinDiagnostics);
	        if (portSymbols.length > 0) {
	            cache.PortSymbol = portSymbols;
	        }
	        if (offPageSymbols.length > 0) {
	            cache.OffPageSymbol = offPageSymbols;
	        }
	        if (globalSymbols.length > 0) {
	            cache.GlobalSymbol = globalSymbols;
	        }
	        if (ercSymbols.length > 0) {
	            cache.ERCSymbol = ercSymbols;
	        }
	        if (titleBlockSymbol) {
	            cache.TitleBlockSymbol = [titleBlockSymbol];
	        }
	        emitDerivedSymbolBoundingBoxDiagnostics(cache, options);
	        if (!cache.Package && !cache.PortSymbol && !cache.OffPageSymbol && !cache.GlobalSymbol && !cache.TitleBlockSymbol) {
	            options.diagnostics?.push({ severity: 'warning', message: 'CFB DSN cache contained no materialized symbols' });
	        }
	        return cache;
	    }
	    function addMissingLiveConnectorSymbols(document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, liveConnectorPins, diagnostics) {
	        const existingPortSymbols = new Set(portSymbols.map((symbol) => symbol.Name));
	        const existingOffPageSymbols = new Set(offPageSymbols.map((symbol) => symbol.Name));
	        const existingGlobalSymbols = new Set(globalSymbols.map((symbol) => symbol.Name));
	        const sourceLibNames = buildSymbolSourceCatalog(document);
	        for (const [symbolName, role] of pageSymbolRoles) {
	            if (role === 'part-inst' || role === 'titleblock') {
	                continue;
	            }
	            const pin = liveConnectorSymbolPin(liveConnectorPins, symbolName, sourceLibNames.get(symbolName) ?? '');
	            const boundingBox = pin ? boundingBoxFromSymbolPins([pin]) : { X1: 0, X2: 0, Y1: 0, Y2: 0 };
	            if (role === 'global' && !existingGlobalSymbols.has(symbolName)) {
	                globalSymbols.push({
	                    Name: symbolName,
	                    SourceLibName: '',
	                    BoundingBox: boundingBox,
	                    Line: [],
	                    PinScalar: symbolPinsFromCorroboratedLivePin(symbolName, pin, diagnostics),
	                });
	                existingGlobalSymbols.add(symbolName);
	                continue;
	            }
	            if (role === 'off-page-connector' && !existingOffPageSymbols.has(symbolName)) {
	                offPageSymbols.push({
	                    Name: symbolName,
	                    SourceLibName: '',
	                    BoundingBox: boundingBox,
	                    PinScalar: pin ? [offPageSymbolPinScalarFromCfb(pin, 0)] : [],
	                });
	                existingOffPageSymbols.add(symbolName);
	                continue;
	            }
	            if (role === 'port' && !existingPortSymbols.has(symbolName)) {
	                portSymbols.push({
	                    Name: symbolName,
	                    SourceLibName: '',
	                    BoundingBox: boundingBox,
	                    ...(pin ? { PinScalar: symbolPinScalarFromCfb(pin) } : {}),
	                });
	                existingPortSymbols.add(symbolName);
	            }
	        }
	    }
	    function groupRootCacheObjects(document, physicalPinDiagnostics) {
	        const cacheStream = document.streams.find((stream) => stream.parsed?.kind === 'cache');
	        if (!cacheStream) {
	            return [];
	        }
	        const sourceLibNamesByOffset = buildCacheSourceLibNameOffsetCatalog(cacheStream);
	        const entries = [
	            ...(cacheStream.decodedSymbolObjects ?? []).map((record) => ({ offset: record.offset, record })),
	            ...(cacheStream.decodedPrimitives ?? []).map((record) => ({ offset: record.offset, record })),
	        ].sort((left, right) => left.offset - right.offset);
	        const groups = [];
	        let current;
	        for (const entry of entries) {
	            const record = entry.record;
	            if (isCacheObjectGroupHeader(record)) {
	                if (current && (current.records.length > 1 || current.primitives.length > 0)) {
	                    groups.push(current);
	                }
	                current = {
	                    name: record.name,
	                    headerKind: record.kind,
	                    sourceLibName: sourceLibNamesByOffset.get(record.offset),
	                    records: [record],
	                    primitives: [],
	                };
	                continue;
	            }
	            if (!current) {
	                continue;
	            }
	            if (isSymbolObjectRecord(record)) {
	                current.records.push(record);
	            }
	            else {
	                current.primitives.push(record);
	            }
	        }
	        if (current && (current.records.length > 1 || current.primitives.length > 0)) {
	            groups.push(current);
	        }
	        if (physicalPinDiagnostics) {
	            reportViewPrimitiveCountMismatches(groups, physicalPinDiagnostics);
	        }
	        return resolveBlankSourceCacheGroups(groups, physicalPinDiagnostics);
	    }
	    function reportViewPrimitiveCountMismatches(groups, diagnostics) {
	        let undecodedHeaders = 0;
	        for (const group of groups) {
	            const header = group.records[0];
	            if (header?.kind !== 'view-header') {
	                continue;
	            }
	            if (header.primitiveCount === undefined) {
	                undecodedHeaders++;
	                continue;
	            }
	            if (header.primitiveCount === group.primitives.length) {
	                continue;
	            }
	            emitPhysicalPinDiagnostic(diagnostics, group.name, 'view-primitive-count-mismatch', `declared=${header.primitiveCount} decoded=${group.primitives.length}`, group.sourceLibName);
	        }
	        if (undecodedHeaders > 0) {
	            emitBudgetedDiagnostic(diagnostics, '', 'view-primitive-count-undecoded', `views=${undecodedHeaders}`, 'CFB symbol', 'info');
	        }
	    }
	    function resolveBlankSourceCacheGroups(groups, diagnostics) {
	        const sourcesByCell = new Map();
	        for (const group of groups) {
	            const canonicalSource = canonicalSourceLibraryName(group.sourceLibName);
	            if (!canonicalSource) {
	                continue;
	            }
	            const cellName = stripNormalViewSuffix(group.name);
	            const sources = sourcesByCell.get(cellName) ?? new Map();
	            sources.set(canonicalSource, group.sourceLibName ?? '');
	            sourcesByCell.set(cellName, sources);
	        }
	        return groups.map((group) => {
	            if (canonicalSourceLibraryName(group.sourceLibName)) {
	                return group;
	            }
	            const cellName = stripNormalViewSuffix(group.name);
	            const sources = sourcesByCell.get(cellName);
	            if ((sources?.size ?? 0) >= 2 && diagnostics) {
	                emitPhysicalPinDiagnostic(diagnostics, cellName, 'blank-source-group', `cell="${cellName}" candidates=${sources?.size ?? 0}`);
	            }
	            return group;
	        });
	    }
	    function buildCacheSourceLibNameOffsetCatalog(cacheStream) {
	        const catalog = new Map();
	        for (const source of cacheStream.cacheSourceLibraries ?? []) {
	            catalog.set(source.headerOffset, source.library.value);
	        }
	        return catalog;
	    }
	    function buildPackageViewPinCatalog(document, cacheCatalog, physicalPinDiagnostics) {
	        const candidatesByIdentity = new Map();
	        const boundsByPins = new Map();
	        for (const group of groupRootCacheObjects(document, physicalPinDiagnostics)) {
	            if (group.headerKind !== 'view-header') {
	                continue;
	            }
	            const cellName = stripNormalViewSuffix(group.name);
	            const entry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName, physicalPinDiagnostics);
	            const packageName = entry?.packageName ?? cellName;
	            const sourceLibName = entry?.sourceLibName ?? group.sourceLibName ?? '';
	            const pins = completeCacheSymbolPins(group);
	            const bounds = group.records.find((record) => record.kind === 'view-header')?.declaredBoundingBox;
	            if (bounds) {
	                boundsByPins.set(pins, JSON.stringify(bounds));
	            }
	            if (pins.length > 0) {
	                for (const viewName of [group.name, cellName]) {
	                    const key = cacheViewPinIdentityKey(viewName, packageName, sourceLibName);
	                    const candidates = candidatesByIdentity.get(key) ?? [];
	                    candidates.push(pins);
	                    candidatesByIdentity.set(key, candidates);
	                }
	            }
	        }
	        const pinsByIdentity = new Map();
	        const ambiguousIdentities = new Set();
	        const pinsWithDeclaredBounds = new Set();
	        for (const [key, candidates] of candidatesByIdentity) {
	            const distinct = new Map(candidates.map((pins) => [cachePinVectorSignature(pins), pins]));
	            if (distinct.size === 1) {
	                for (const pins of distinct.values()) {
	                    pinsByIdentity.set(key, pins);
	                    if (candidates.every((candidate) => boundsByPins.has(candidate)) &&
	                        new Set(candidates.map((candidate) => boundsByPins.get(candidate))).size === 1) {
	                        pinsWithDeclaredBounds.add(pins);
	                    }
	                }
	            }
	            else {
	                ambiguousIdentities.add(key);
	            }
	        }
	        return { pinsByIdentity, ambiguousIdentities, pinsWithDeclaredBounds };
	    }
	    function cacheViewPinIdentityKey(viewName, packageName, sourceLibName) {
	        return `${cacheIdentityKey(stripNormalViewSuffix(viewName), packageName, sourceLibName)}\0${viewName.toUpperCase()}`;
	    }
	    function cachePinVectorSignature(pins) {
	        return JSON.stringify(pins.map((pin) => [pin.name, pin.startX, pin.startY, pin.hotptX, pin.hotptY]));
	    }
	    function cachePinsForPlacedGraphic(catalog, object, cellName, entry, diagnostics) {
	        const exactKey = cacheViewPinIdentityKey(object.graphicName, entry.packageName, entry.canonicalSourceLibName);
	        const exactPins = catalog.pinsByIdentity.get(exactKey);
	        if (exactPins) {
	            return exactPins;
	        }
	        const cellKey = cacheViewPinIdentityKey(cellName, entry.packageName, entry.canonicalSourceLibName);
	        if (catalog.ambiguousIdentities.has(cellKey)) {
	            emitPhysicalPinDiagnostic(diagnostics, cellName, 'cache-view-pin-ambiguous', `cell="${cellName}" package="${entry.packageName}" library="${entry.sourceLibName}" graphic="${object.graphicName}"`, entry.sourceLibName);
	            return undefined;
	        }
	        return catalog.pinsByIdentity.get(cellKey);
	    }
	    function buildConnectorSymbolGeometryCatalog(document, diagnostics, partValueConnectorGeometryGroups, cache, liveConnectorPins) {
	        const candidatesByIdentity = new Map();
	        const candidatesBySymbol = new Map();
	        const cacheCandidateCountByIdentity = new Map();
	        const cacheCandidateCountBySymbol = new Map();
	        const evidenceIdentityKeys = new Set();
	        const evidenceSymbolKeys = new Set();
	        const sourceLibrariesBySymbol = buildSymbolSourceLibraryCatalog(document);
	        const connectorSymbolKeys = new Set(Array.from(collectPageSymbolRoles(document))
	            .filter(([, role]) => role === 'off-page-connector' || role === 'global')
	            .map(([name]) => liveConnectorPinSymbolKey(name)));
	        const addCandidate = (symbolName, sourceLibName, geometry, preferCacheOrder = false) => {
	            const identityKey = liveConnectorPinIdentityKey(symbolName, sourceLibName);
	            evidenceIdentityKeys.add(identityKey);
	            const identityCandidates = candidatesByIdentity.get(identityKey) ?? [];
	            if (preferCacheOrder) {
	                const cacheCandidateCount = cacheCandidateCountByIdentity.get(identityKey) ?? 0;
	                identityCandidates.splice(cacheCandidateCount, 0, geometry);
	                cacheCandidateCountByIdentity.set(identityKey, cacheCandidateCount + 1);
	            }
	            else {
	                identityCandidates.push(geometry);
	            }
	            candidatesByIdentity.set(identityKey, identityCandidates);
	            const symbolKey = liveConnectorPinSymbolKey(symbolName);
	            evidenceSymbolKeys.add(symbolKey);
	            const symbolCandidates = candidatesBySymbol.get(symbolKey) ?? [];
	            if (preferCacheOrder) {
	                const cacheCandidateCount = cacheCandidateCountBySymbol.get(symbolKey) ?? 0;
	                symbolCandidates.splice(cacheCandidateCount, 0, geometry);
	                cacheCandidateCountBySymbol.set(symbolKey, cacheCandidateCount + 1);
	            }
	            else {
	                symbolCandidates.push(geometry);
	            }
	            candidatesBySymbol.set(symbolKey, symbolCandidates);
	        };
	        for (const stream of document.streams) {
	            if (stream.parsed?.kind !== 'symbol') {
	                continue;
	            }
	            const pins = (stream.decodedSymbolObjects ?? []).filter(isSymbolPin);
	            if (pins.length === 0) {
	                continue;
	            }
	            const symbolName = stream.parsed.symbolName;
	            if (!connectorSymbolKeys.has(liveConnectorPinSymbolKey(symbolName))) {
	                continue;
	            }
	            const geometry = {
	                pins,
	                boundingBox: connectorSymbolBoundingBox(stream.decodedPrimitives ?? [], pins),
	                hasDecodedBounds: (stream.decodedPrimitives?.length ?? 0) > 0,
	            };
	            for (const sourceLibName of sourceLibrariesBySymbol.get(symbolName) ?? ['']) {
	                addCandidate(symbolName, sourceLibName, geometry);
	            }
	        }
	        for (const group of groupRootCacheObjects(document, diagnostics)) {
	            const pins = group.records.filter(isSymbolPin);
	            if (pins.length === 0 || !connectorSymbolKeys.has(liveConnectorPinSymbolKey(group.name))) {
	                continue;
	            }
	            addCandidate(group.name, group.sourceLibName ?? '', {
	                pins,
	                boundingBox: connectorSymbolBoundingBox(group.primitives, pins),
	                hasDecodedBounds: group.primitives.length > 0,
	            }, true);
	        }
	        for (const group of partValueConnectorGeometryGroups) {
	            const pins = group.records.filter(isSymbolPin);
	            if (pins.length === 0) {
	                continue;
	            }
	            addCandidate(group.name, group.sourceLibName ?? '', {
	                pins,
	                boundingBox: connectorSymbolBoundingBox(group.primitives, pins),
	                hasDecodedBounds: group.primitives.length > 0,
	            }, true);
	        }
	        for (const key of liveConnectorPins.byIdentity.keys()) {
	            evidenceIdentityKeys.add(key);
	        }
	        for (const key of liveConnectorPins.bySymbol.keys()) {
	            evidenceSymbolKeys.add(key);
	        }
	        const emittedByIdentity = new Map();
	        const emittedBySymbol = new Map();
	        for (const symbol of [...(cache.OffPageSymbol ?? []), ...(cache.GlobalSymbol ?? [])]) {
	            const sourceLibName = symbol.SourceLibName ?? '';
	            const identityKey = liveConnectorPinIdentityKey(symbol.Name, sourceLibName);
	            const symbolKey = liveConnectorPinSymbolKey(symbol.Name);
	            if (!evidenceIdentityKeys.has(identityKey) && !evidenceSymbolKeys.has(symbolKey)) {
	                continue;
	            }
	            const pins = (Array.isArray(symbol.PinScalar) ? symbol.PinScalar : [symbol.PinScalar]).filter((pin) => pin !== undefined);
	            if (pins.length === 0) {
	                emitPhysicalPinDiagnostic(diagnostics, symbol.Name, 'connector-pin-absent', `symbol="${symbol.Name}"`, sourceLibName, 'info');
	            }
	            const geometry = {
	                pins: pins.map(connectorPinGeometryFromLogicalPin),
	                boundingBox: symbol.BoundingBox,
	                hasDecodedBounds: true,
	            };
	            const identityCandidates = emittedByIdentity.get(identityKey) ?? [];
	            identityCandidates.push(geometry);
	            emittedByIdentity.set(identityKey, identityCandidates);
	            const symbolCandidates = emittedBySymbol.get(symbolKey) ?? [];
	            symbolCandidates.push(geometry);
	            emittedBySymbol.set(symbolKey, symbolCandidates);
	        }
	        for (const [key, values] of emittedByIdentity) {
	            candidatesByIdentity.set(key, connectorCandidatesWithPinGeometry(candidatesByIdentity.get(key) ?? [], values));
	        }
	        for (const [key, values] of emittedBySymbol) {
	            candidatesBySymbol.set(key, connectorCandidatesWithPinGeometry(candidatesBySymbol.get(key) ?? [], values));
	        }
	        return {
	            byIdentity: corroboratedConnectorSymbolGeometries(candidatesByIdentity, diagnostics, true),
	            bySymbol: corroboratedConnectorSymbolGeometries(candidatesBySymbol, diagnostics, false),
	            candidatesBySymbol: new Map(Array.from(candidatesBySymbol, ([key, values]) => [
	                key,
	                Array.from(new Map(values.map((value) => [connectorSymbolGeometryKey(value), value])).values()),
	            ])),
	        };
	    }
	    function connectorCandidatesWithPinGeometry(observed, published) {
	        if (published.some((geometry) => geometry.pins.length > 0)) {
	            return [...published];
	        }
	        const pinned = observed.filter((geometry) => geometry.pins.length > 0);
	        return pinned.length > 0 ? pinned : [...published];
	    }
	    function collectPartValueConnectorGeometryGroups(document) {
	        const cacheStream = document.streams.find((stream) => stream.parsed?.kind === 'cache');
	        if (!cacheStream) {
	            return [];
	        }
	        const connectorNames = new Set(Array.from(collectPageSymbolRoles(document))
	            .filter(([, role]) => role === 'off-page-connector' || role === 'global')
	            .map(([name]) => liveConnectorPinSymbolKey(name)));
	        const entries = [
	            ...(cacheStream.decodedSymbolObjects ?? []).map((record) => ({ offset: record.offset, record })),
	            ...(cacheStream.decodedPrimitives ?? []).map((record) => ({ offset: record.offset, record })),
	        ].sort((left, right) => left.offset - right.offset);
	        const groups = [];
	        for (let index = 0; index < entries.length; index++) {
	            const header = entries[index]?.record;
	            if (!header || !isSymbolObjectRecord(header)) {
	                continue;
	            }
	            const headerIdentity = partValueConnectorHeaderIdentity(header, connectorNames);
	            if (!headerIdentity) {
	                continue;
	            }
	            const records = [header];
	            const primitives = [];
	            for (let bodyIndex = index + 1; bodyIndex < entries.length; bodyIndex++) {
	                const record = entries[bodyIndex]?.record;
	                if (!record) {
	                    break;
	                }
	                if (isSymbolObjectRecord(record) &&
	                    (isCacheObjectGroupHeader(record) || partValueConnectorHeaderIdentity(record, connectorNames))) {
	                    break;
	                }
	                if (isSymbolObjectRecord(record)) {
	                    records.push(record);
	                }
	                else {
	                    primitives.push(record);
	                }
	            }
	            if (records.some(isSymbolPin)) {
	                groups.push({
	                    ...headerIdentity,
	                    headerKind: 'named-record',
	                    records,
	                    primitives,
	                });
	            }
	        }
	        return groups;
	    }
	    function connectorPinGeometryFromLogicalPin(pin) {
	        return {
	            name: pin.Name,
	            startX: pin.StartX,
	            startY: pin.StartY,
	            hotptX: pin.HotptX,
	            hotptY: pin.HotptY,
	        };
	    }
	    function connectorSymbolBoundingBox(primitives, pins) {
	        return primitives.length > 0 ?
	            boundingBoxFromGeometry(primitiveGeometry([...primitives]))
	            : boundingBoxFromSymbolPins([...pins]);
	    }
	    function partValueConnectorHeaderIdentity(record, connectorNames) {
	        if (record.kind !== 'part-value') {
	            return undefined;
	        }
	        const name = record.strings[0]?.value;
	        const sourceLibName = record.strings.find((entry) => isSourceLibraryPath(entry.value))?.value;
	        if (!name || !sourceLibName || !connectorNames.has(liveConnectorPinSymbolKey(name))) {
	            return undefined;
	        }
	        return { name, sourceLibName };
	    }
	    function corroboratedConnectorSymbolGeometries(candidates, diagnostics, identityScoped) {
	        const geometries = new Map();
	        for (const [key, values] of candidates) {
	            const geometryKeys = new Set(values.map(connectorSymbolGeometryKey));
	            if (geometryKeys.size === 1 && values[0]) {
	                geometries.set(key, values[0]);
	                continue;
	            }
	            const [symbolName, sourceLibName = ''] = identityScoped ? key.split('\0') : [key, ''];
	            emitBudgetedDiagnostic(diagnostics, key, 'connector-pin-geometry-conflict', `symbol="${symbolName}" src="${sourceLibName}" geometries=${geometryKeys.size}`, 'CFB connector', 'warning');
	        }
	        return geometries;
	    }
	    function connectorSymbolGeometryKey(geometry) {
	        const box = geometry.boundingBox;
	        return [
	            box.X1,
	            box.X2,
	            box.Y1,
	            box.Y2,
	            ...geometry.pins.flatMap((pin) => [pin.name, pin.hotptX, pin.hotptY, pin.startX, pin.startY]),
	        ].join(',');
	    }
	    function populatePlacedSymbolSourceLibraries(document, placedPartCacheIdentityCatalog, markerSourceLibraries, diagnostics) {
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol') {
	                    continue;
	                }
	                const partIdentity = placedPartCacheIdentityCatalog.get(object);
	                if (object.role === 'part-inst') {
	                    if (partIdentity?.sourceLibName && object.resolvedMetadata) {
	                        object.resolvedMetadata.libName = partIdentity.sourceLibName;
	                    }
	                    continue;
	                }
	                const markerSource = markerSourceLibraries.get(object);
	                const explicitSource = object.resolvedMetadata?.libName;
	                const sources = [markerSource, explicitSource].filter((source) => Boolean(source));
	                const identities = new Set(sources.map(canonicalSourceLibraryName));
	                object.resolvedMetadata ??= { kind: object.role };
	                if (identities.size === 1) {
	                    object.resolvedMetadata.libName = markerSource ?? explicitSource;
	                    continue;
	                }
	                object.resolvedMetadata.libName = '';
	                emitDecisionDiagnostic(diagnostics, identities.size > 1 ? 'connector-library-source-conflict' : 'connector-library-unresolved', `symbol="${object.symbolName}" page="${stream.path}" offset=${object.offset} dbId=${object.dbId} marker=${JSON.stringify(markerSource ?? '')} explicit=${JSON.stringify(explicitSource ?? '')}: source library withheld`, 'CFB connector', 'warning');
	            }
	        }
	    }
	    function buildRootCachePackages(cacheObjectGroups, cacheCatalog, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence) {
	        const packageGroups = new Map();
	        for (const group of cacheObjectGroups) {
	            if (group.headerKind !== 'view-header') {
	                continue;
	            }
	            const catalogEntry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName, physicalPinDiagnostics);
	            const pkgName = catalogEntry?.packageName ?? stripNormalViewSuffix(group.name);
	            const sourceLibName = catalogEntry?.sourceLibName ?? group.sourceLibName ?? '';
	            if (pkgName.length === 0) {
	                continue;
	            }
	            const key = packageSourceKey(pkgName, sourceLibName);
	            const existing = packageGroups.get(key) ?? { pkgName, sourceLibName, catalogEntry, groups: [] };
	            existing.catalogEntry ??= catalogEntry;
	            existing.groups.push(group);
	            packageGroups.set(key, existing);
	        }
	        return Array.from(packageGroups.values()).map(({ pkgName, sourceLibName, catalogEntry, groups }) => buildPackageFromCacheGroups(pkgName, sourceLibName, groups, cacheCatalog, catalogEntry, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence));
	    }
	    function buildPackageFromCacheGroups(pkgName, sourceLibName, groups, cacheCatalog, catalogEntry, physicalPinNumberCatalog, physicalPadSectionCounts, sectionNumberingStyles, physicalPinDiagnostics, placedViewEvidence) {
	        const markedSectionNumberingStyle = resolveSectionNumberingStyle(sectionNumberingStyles, pkgName, sourceLibName);
	        const materializedGroups = groups
	            .filter(isXmlMaterializedPackageViewGroup)
	            .sort((left, right) => comparePackageSectionNames(stripNormalViewSuffix(left.name), stripNormalViewSuffix(right.name)));
	        const orderedGroups = selectCacheGroupsByCellName(materializedGroups, (group) => placedViewEvidence.get(cacheIdentityKey(stripNormalViewSuffix(group.name), pkgName, catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName ?? sourceLibName, physicalPinDiagnostics)?.sourceLibName ??
	            group.sourceLibName ??
	            sourceLibName)) ?? [], physicalPinDiagnostics);
	        const firstPartValue = groups.flatMap((group) => group.records).find(isPartValue);
	        const attestsPhysicalPads = (firstPartValue?.value ?? '') !== '';
	        const libPartPadOutcomes = [];
	        const libParts = orderedGroups.flatMap((group) => {
	            const groupCatalogEntry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName ?? sourceLibName, physicalPinDiagnostics) ?? catalogEntry;
	            const cellName = stripNormalViewSuffix(group.name);
	            const groupSourceLibName = groupCatalogEntry?.sourceLibName ?? group.sourceLibName ?? sourceLibName;
	            const directPins = completeCacheSymbolPins(group);
	            const pinMaps = physicalPinNumberCatalog.get(cacheIdentityKey(cellName, pkgName, groupSourceLibName));
	            const expectedArities = new Set((pinMaps ?? []).map((map) => map.pinNumbers.length));
	            const pins = directPins.length > 0 ?
	                directPins
	                : corroboratedSiblingViewPins(group, materializedGroups.map((candidate) => {
	                    const candidateEntry = catalogEntryForGraphicName(cacheCatalog, candidate.name, candidate.sourceLibName ?? sourceLibName, physicalPinDiagnostics) ?? catalogEntry;
	                    return {
	                        group: candidate,
	                        cellName: stripNormalViewSuffix(candidate.name),
	                        packageName: candidateEntry?.packageName ?? pkgName,
	                        sourceLibName: candidateEntry?.sourceLibName ?? candidate.sourceLibName ?? sourceLibName,
	                        pins: completeCacheSymbolPins(candidate),
	                    };
	                }), {
	                    cellName,
	                    packageName: pkgName,
	                    sourceLibName: groupSourceLibName,
	                    expectedArities,
	                    hasInformativePinMap: (pinMaps ?? []).some((map) => !isOrdinalPinNumberMap(map.pinNumbers)),
	                    placedObjects: placedViewEvidence.get(cacheIdentityKey(cellName, pkgName, groupSourceLibName)) ?? [],
	                }, physicalPinDiagnostics);
	            const normalView = buildNormalView(group, pins);
	            if (isEmptyNormalView(normalView)) {
	                return [];
	            }
	            const padResolution = pinNumberMapsForPackage(physicalPinNumberCatalog, physicalPadSectionCounts, stripNormalViewSuffix(group.name), pkgName, groupCatalogEntry?.canonicalSourceLibName ?? sourceLibName, pins, physicalPinDiagnostics);
	            libPartPadOutcomes.push(padResolution.outcome);
	            return [
	                {
	                    CellName: stripNormalViewSuffix(group.name),
	                    NormalView: [normalView],
	                    PhysicalPart: buildPhysicalPartsFromPins(pins, padResolution, attestsPhysicalPads, physicalPinDiagnostics, stripNormalViewSuffix(group.name), groupCatalogEntry?.canonicalSourceLibName ?? sourceLibName),
	                },
	            ];
	        });
	        const referencePrefix = catalogEntry?.referencePrefix ?? firstPartValue?.referencePrefix;
	        if (referencePrefix === undefined) {
	            emitBudgetedDiagnostic(physicalPinDiagnostics, `${pkgName}\0${canonicalSourceLibraryName(sourceLibName)}`, 'package-reference-prefix-unrecorded', `package "${pkgName}" records no reference prefix, so it declares none`, 'CFB DSN package', 'info');
	        }
	        return {
	            pkg: {
	                AlphabeticNumbering: markedSectionNumberingStyle ?? (libParts.length > 1 ? 1 : 2),
	                IsHomogeneous: libParts.length <= 1,
	                Name: pkgName,
	                PcbFootprint: firstPartValue?.value ?? '',
	                PcbLib: '',
	                RefdesPrefix: referencePrefix ?? '',
	                SourceLibName: catalogEntry?.sourceLibName ?? sourceLibName,
	                LibPart: libParts,
	            },
	            markedSectionNumberingStyle,
	            fallbackCellNames: new Set(libParts
	                .filter((_libPart, index) => libPartPadOutcomes[index] !== PhysicalPadOutcome.Decoded)
	                .map((libPart) => stripNormalViewSuffix(libPart.CellName))),
	        };
	    }
	    function comparePackageSectionNames(left, right) {
	        return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
	    }
	    function selectCacheGroupsByCellName(groups, placedObjectsForGroup, diagnostics) {
	        const groupsByCellName = new Map();
	        for (const group of groups) {
	            const cellName = stripNormalViewSuffix(group.name);
	            groupsByCellName.set(cellName, [...(groupsByCellName.get(cellName) ?? []), group]);
	        }
	        const selected = [];
	        const seenCellNames = new Set();
	        for (const group of groups) {
	            const cellName = stripNormalViewSuffix(group.name);
	            if (seenCellNames.has(cellName)) {
	                continue;
	            }
	            seenCellNames.add(cellName);
	            selected.push(placementCorroboratedCacheGroup(groupsByCellName.get(cellName) ?? [group], placedObjectsForGroup, cellName, diagnostics) ?? group);
	        }
	        return selected;
	    }
	    function recordedPlacedPortArity(object) {
	        const positions = new Set((object.portInstances ?? []).map((portInstance) => portInstance.position));
	        if (positions.size === 0) {
	            return undefined;
	        }
	        return [...positions].every((position) => position >= 0 && position < positions.size) ? positions.size : undefined;
	    }
	    function placementCorroboratedCacheGroup(groups, placedObjectsForGroup, cellName, diagnostics) {
	        const definitions = groups.filter((group) => group.records.some(isSymbolPin));
	        const arities = new Set(definitions.map((group) => group.records.filter(isSymbolPin).length));
	        if (definitions.length < 2 || arities.size < 2) {
	            return undefined;
	        }
	        const recordedArities = new Set();
	        for (const group of definitions) {
	            for (const object of placedObjectsForGroup(group)) {
	                const recordedArity = recordedPlacedPortArity(object);
	                if (recordedArity !== undefined) {
	                    recordedArities.add(recordedArity);
	                }
	            }
	        }
	        const corroborated = recordedArities.size === 1 ?
	            definitions.filter((group) => recordedArities.has(group.records.filter(isSymbolPin).length))
	            : [];
	        if (corroborated.length !== 1) {
	            emitPhysicalPinDiagnostic(diagnostics, cellName, 'same-name-view-arity-unresolved', `cell="${cellName}" arities=${[...arities].join(',')} recorded=${[...recordedArities].join(',')}`, corroborated[0]?.sourceLibName ?? definitions[0]?.sourceLibName);
	            return undefined;
	        }
	        emitPhysicalPinDiagnostic(diagnostics, cellName, 'same-name-view-arity-selected', `cell="${cellName}" arities=${[...arities].join(',')} chose=${[...recordedArities][0]}`, corroborated[0].sourceLibName, 'info');
	        return corroborated[0];
	    }
	    function isXmlMaterializedPackageViewGroup(group) {
	        return !/\.(?:Convert|IEEE)$/i.test(group.name ?? '');
	    }
	    function compareXmlTextOrder(left, right) {
	        if (left === right) {
	            return 0;
	        }
	        return left < right ? -1 : 1;
	    }
	    function isEmptyNormalView(normalView) {
	        return !(normalView.PinScalar?.length ||
	            normalView.PinBus?.length ||
	            normalView.Line?.length ||
	            normalView.Rectangle?.length ||
	            normalView.Ellipse?.length ||
	            normalView.Polygon?.length ||
	            normalView.Polyline?.length ||
	            normalView.Arc?.length ||
	            normalView.CommentText?.length ||
	            normalView.Bitmap?.length);
	    }
	    function completeCacheSymbolPins(group) {
	        return group.records.filter(isSymbolPin);
	    }
	    function corroboratedSiblingViewPins(targetGroup, candidates, target, diagnostics) {
	        const localTargetGeometry = siblingViewLocalGeometry(targetGroup, candidates);
	        const targetGeometry = Object.keys(localTargetGeometry).length > 0 ? localTargetGeometry
	            : target.hasInformativePinMap ? primitiveGeometry(targetGroup.primitives)
	                : {};
	        const geometryKey = siblingViewGeometryKey(targetGeometry);
	        const expectedArities = [...target.expectedArities];
	        const matches = geometryKey === undefined || expectedArities.length !== 1 ?
	            []
	            : candidates.filter((candidate) => candidate.group !== targetGroup &&
	                candidate.cellName === target.cellName &&
	                candidate.packageName === target.packageName &&
	                canonicalSourceLibraryName(candidate.sourceLibName) === canonicalSourceLibraryName(target.sourceLibName) &&
	                candidate.pins.length === expectedArities[0] &&
	                target.placedObjects.some((object) => (object.materializedPortInstances?.length ?? 0) === candidate.pins.length &&
	                    placedPartGeometryMatchesCachePins(object, candidate.pins)) &&
	                (siblingViewGeometryKey(siblingViewLocalGeometry(candidate.group, candidates)) === geometryKey ||
	                    (target.hasInformativePinMap &&
	                        siblingViewGeometryKey(primitiveGeometry(candidate.group.primitives)) === geometryKey) ||
	                    symbolPinsAttachToGeometry(candidate.pins, targetGeometry)));
	        const signatures = new Map();
	        for (const match of matches) {
	            signatures.set(symbolPinGeometrySignature(match.pins), match.pins);
	        }
	        if (signatures.size === 1) {
	            return [...(signatures.values().next().value ?? [])];
	        }
	        if (target.placedObjects.length > 0) {
	            emitPhysicalPinDiagnostic(diagnostics, target.cellName, 'view-pinless', `cell="${target.cellName}" package="${target.packageName}" src="${target.sourceLibName}" siblingCandidates=${matches.length}`, target.sourceLibName);
	        }
	        return [];
	    }
	    function siblingViewLocalGeometry(group, candidates) {
	        const startOffset = group.records[0]?.offset;
	        if (startOffset === undefined) {
	            return {};
	        }
	        const endOffset = candidates
	            .map((candidate) => candidate.group.records[0]?.offset)
	            .filter((offset) => offset !== undefined && offset > startOffset)
	            .sort((left, right) => left - right)[0];
	        return primitiveGeometry(group.primitives.filter((primitive) => primitive.offset > startOffset && (endOffset === undefined || primitive.offset < endOffset)));
	    }
	    function siblingViewGeometryKey(geometry) {
	        return Object.keys(geometry).length > 0 ? JSON.stringify(geometry) : undefined;
	    }
	    function symbolPinsAttachToGeometry(pins, geometry) {
	        if (Object.keys(geometry).length === 0) {
	            return false;
	        }
	        const bounds = boundingBoxFromGeometry(geometry, { excludeTextWhenPossible: true });
	        return pins.every((pin) => pin.startX >= bounds.X1 && pin.startX <= bounds.X2 && pin.startY >= bounds.Y1 && pin.startY <= bounds.Y2);
	    }
	    function symbolPinGeometrySignature(pins) {
	        return pins
	            .map((pin) => [pin.name, pin.startX, pin.startY, pin.hotptX, pin.hotptY, pin.pinType ?? ''].join(','))
	            .join(';');
	    }
	    function buildPhysicalPartFromPins(pins, attestsPhysicalPads, pinNumbers, physicalPinDiagnostics, cellName, sourceLibName) {
	        if (!pinNumbers && physicalPinDiagnostics && pins.length > 0) {
	            emitPhysicalPinDiagnostic(physicalPinDiagnostics, cellName ?? 'unknown', attestsPhysicalPads ? 'pad-numbers-undecoded' : 'fallback-pin-names', attestsPhysicalPads ?
	                `cell "${cellName ?? 'unknown'}" attests pads no row numbered, so its pin names stand in for them`
	                : `cell "${cellName ?? 'unknown'}" attests no pads, so its positions are keyed by pin name`, sourceLibName);
	        }
	        return {
	            PinNumber: pins.map((pin, position) => ({ Position: position, Number: pinNumbers?.[position] ?? pin.name })),
	        };
	    }
	    function buildPhysicalPartsFromPins(pins, resolution, attestsPhysicalPads, physicalPinDiagnostics, cellName, sourceLibName) {
	        const decodedMaps = resolution.pinNumberMaps ?? [];
	        const sectionCount = Math.max(resolution.sectionCount, decodedMaps.length);
	        if (physicalPinDiagnostics && decodedMaps.length > 0 && decodedMaps.length < sectionCount) {
	            const undecodedSections = Array.from({ length: sectionCount }, (_unused, index) => index + 1).slice(decodedMaps.length);
	            emitPhysicalPinDiagnostic(physicalPinDiagnostics, cellName ?? 'unknown', 'section-pads-partial', `cell="${cellName ?? 'unknown'}" sections=${sectionCount} decoded=${decodedMaps.length} fallback=${undecodedSections.join(',')}`, sourceLibName);
	        }
	        return Array.from({ length: sectionCount }, (_unused, index) => buildPhysicalPartFromPins(pins, attestsPhysicalPads, decodedMaps[index], physicalPinDiagnostics, cellName, sourceLibName));
	    }
	    function arePinNumberMapsPairwiseDisjoint(maps) {
	        for (let i = 0; i < maps.length; i++) {
	            const seen = new Set(maps[i]);
	            for (let j = i + 1; j < maps.length; j++) {
	                if (maps[j].some((value) => seen.has(value))) {
	                    return false;
	                }
	            }
	        }
	        return true;
	    }
	    function reportUndecodedPinLabelVisibility(viewCount, diagnostics) {
	        if (viewCount === 0) {
	            return;
	        }
	        emitBudgetedDiagnostic(diagnostics, '', 'view-pin-visibility-undecoded', `views=${viewCount}`, 'CFB symbol', 'info');
	    }
	    function addRootCacheConnectorSymbols(cacheObjectGroups, document, pageSymbolRoles, portSymbols, offPageSymbols, globalSymbols, ercSymbols, liveConnectorPins, diagnostics) {
	        const sourceLibNames = buildSymbolSourceCatalog(document);
	        const sourceLibNamesBySymbol = buildSymbolSourceLibraryCatalog(document);
	        const existingPortSymbols = new Set(portSymbols.map((symbol) => packageSourceKey(symbol.Name, symbol.SourceLibName ?? '')));
	        const existingOffPageSymbols = new Set(offPageSymbols.map((symbol) => packageSourceKey(symbol.Name, symbol.SourceLibName ?? '')));
	        const existingGlobalSymbols = new Set(globalSymbols.map((symbol) => packageSourceKey(symbol.Name, symbol.SourceLibName ?? '')));
	        const existingErcSymbols = new Set(ercSymbols.map((symbol) => symbol.Name));
	        for (const group of cacheObjectGroups) {
	            if (group.headerKind !== 'named-record') {
	                continue;
	            }
	            const name = group.name;
	            const role = pageSymbolRoles.get(name);
	            if (isPortSymbolName(name, pageSymbolRoles)) {
	                const sources = sourceLibNamesBySymbol.get(name) ?? [sourceLibNames.get(name) ?? ''];
	                for (const sourceLibName of sources) {
	                    const key = packageSourceKey(name, sourceLibName);
	                    if (existingPortSymbols.has(key)) {
	                        continue;
	                    }
	                    portSymbols.push(buildPortSymbolFromCacheGroup(group, sourceLibName, diagnostics));
	                    existingPortSymbols.add(key);
	                }
	                continue;
	            }
	            if (role === 'off-page-connector' || isOffPageSymbolName(name)) {
	                const sourceLibName = group.sourceLibName ?? sourceLibNames.get(name) ?? '';
	                const key = packageSourceKey(name, sourceLibName);
	                if (existingOffPageSymbols.has(key)) {
	                    continue;
	                }
	                offPageSymbols.push(buildOffPageSymbolFromCacheGroup(group, sourceLibName));
	                existingOffPageSymbols.add(key);
	                continue;
	            }
	            if (role === 'global' || isCapsymPowerSymbolName(name)) {
	                const sources = sourceLibNamesBySymbol.get(name) ?? [sourceLibNames.get(name) ?? ''];
	                for (const sourceLibName of sources) {
	                    const key = packageSourceKey(name, sourceLibName);
	                    if (existingGlobalSymbols.has(key)) {
	                        continue;
	                    }
	                    globalSymbols.push(buildGlobalSymbolFromCacheGroup(group, sourceLibName, liveConnectorPins, diagnostics));
	                    existingGlobalSymbols.add(key);
	                }
	                continue;
	            }
	            if (isErcSymbolName(name) && !existingErcSymbols.has(name)) {
	                ercSymbols.push(buildErcSymbolFromCacheGroup(group, sourceLibNames.get(name)));
	                existingErcSymbols.add(name);
	            }
	        }
	    }
	    function buildLiveConnectorSymbolPinCatalog(document, diagnostics) {
	        const candidatesByIdentity = new Map();
	        const candidatesBySymbol = new Map();
	        const recordedKeys = new Set();
	        for (const stream of document.streams) {
	            const wires = (stream.decodedPageObjects ?? []).filter(isWire);
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol' || object.role === 'part-inst' || object.role === 'titleblock') {
	                    continue;
	                }
	                const sourceLibName = object.resolvedMetadata?.libName ?? '';
	                const materializedPins = object.materializedPortInstances ?? [];
	                const isRecorded = materializedPins.length > 0;
	                const pins = isRecorded ?
	                    materializedPins.map((materializedPin) => ({
	                        kind: 'symbol-pin',
	                        offset: object.offset,
	                        byteLength: object.byteLength,
	                        payloadByteLength: object.payloadByteLength,
	                        name: materializedPin.name,
	                        startX: materializedPin.startX ?? 0,
	                        startY: materializedPin.startY ?? 0,
	                        hotptX: materializedPin.hotptX ?? materializedPin.x ?? 0,
	                        hotptY: materializedPin.hotptY ?? materializedPin.y ?? 0,
	                    }))
	                    : inferredUnrotatedLiveConnectorPins(object, wires);
	                for (const pin of pins) {
	                    const identityKey = liveConnectorPinIdentityKey(object.symbolName, sourceLibName);
	                    const symbolKey = liveConnectorPinSymbolKey(object.symbolName);
	                    if (isRecorded) {
	                        recordedKeys.add(identityKey);
	                        recordedKeys.add(symbolKey);
	                    }
	                    const identityCandidates = candidatesByIdentity.get(identityKey) ?? [];
	                    identityCandidates.push(pin);
	                    candidatesByIdentity.set(identityKey, identityCandidates);
	                    const symbolCandidates = candidatesBySymbol.get(symbolKey) ?? [];
	                    symbolCandidates.push(pin);
	                    candidatesBySymbol.set(symbolKey, symbolCandidates);
	                }
	            }
	        }
	        const byIdentity = corroboratedLiveConnectorPins(candidatesByIdentity, recordedKeys, diagnostics, true);
	        const bySymbol = corroboratedLiveConnectorPins(candidatesBySymbol, recordedKeys, diagnostics, false);
	        return { byIdentity, bySymbol };
	    }
	    function inferredUnrotatedLiveConnectorPins(object, wires) {
	        if ((object.resolvedMetadata?.rotation ?? 0) !== 0 || (object.resolvedMetadata?.mirror ?? 0) !== 0) {
	            return [];
	        }
	        const point = placedSymbolConnectionPoints(object).points.find((candidate) => wires.some((wire) => isWireEndpoint(wire, candidate.x, candidate.y)));
	        if (!point) {
	            return [];
	        }
	        const hotptX = point.x - object.locX;
	        const hotptY = point.y - object.locY;
	        return [
	            {
	                kind: 'symbol-pin',
	                offset: object.offset,
	                byteLength: object.byteLength,
	                payloadByteLength: object.payloadByteLength,
	                name: object.symbolName,
	                startX: hotptX,
	                startY: hotptY,
	                hotptX,
	                hotptY,
	            },
	        ];
	    }
	    function corroboratedLiveConnectorPins(candidates, recordedKeys, diagnostics, identityScoped) {
	        const pins = new Map();
	        for (const [key, values] of candidates) {
	            const geometries = new Set(values.map(liveConnectorPinGeometryKey));
	            const [symbolName, sourceLibName = ''] = identityScoped ? key.split('\0') : [key, ''];
	            if (geometries.size === 1 && values[0]) {
	                if (!recordedKeys.has(key) && values.length < 2) {
	                    emitPhysicalPinDiagnostic(diagnostics, symbolName, 'live-pin-uncorroborated', `symbol="${symbolName}" src="${sourceLibName}" placements=${values.length}`, sourceLibName);
	                    continue;
	                }
	                pins.set(key, values[0]);
	                continue;
	            }
	            emitPhysicalPinDiagnostic(diagnostics, symbolName, 'live-pin-geometry-conflict', `symbol="${symbolName}" src="${sourceLibName}" geometries=${geometries.size}`, sourceLibName);
	        }
	        return pins;
	    }
	    function liveConnectorSymbolPin(catalog, symbolName, sourceLibName) {
	        return (catalog.byIdentity.get(liveConnectorPinIdentityKey(symbolName, sourceLibName)) ??
	            catalog.bySymbol.get(liveConnectorPinSymbolKey(symbolName)));
	    }
	    function liveConnectorPinIdentityKey(symbolName, sourceLibName) {
	        return `${liveConnectorPinSymbolKey(symbolName)}\0${canonicalSourceLibraryName(sourceLibName)}`;
	    }
	    function liveConnectorPinSymbolKey(symbolName) {
	        return stripNormalViewSuffix(symbolName).toUpperCase();
	    }
	    function liveConnectorPinGeometryKey(pin) {
	        return `${pin.hotptX},${pin.hotptY},${pin.startX},${pin.startY}`;
	    }
	    function diagnoseMissingSourcePackageBodies(document, packages, diagnostics) {
	        const existingKeys = new Set(packages.flatMap((pkg) => [
	            packageSourceKey(pkg.Name, pkg.SourceLibName),
	            ...pkg.LibPart.map((part) => packageSourceKey(part.CellName ?? pkg.Name, pkg.SourceLibName)),
	        ]));
	        for (const entry of collectPackageLibraryEntries(document)) {
	            const key = packageSourceKey(entry.packageName, entry.sourceLibName);
	            if (existingKeys.has(key)) {
	                continue;
	            }
	            emitDecisionDiagnostic(diagnostics, 'package-library-body-missing', `name="${entry.packageName}" src="${entry.sourceLibName}": catalogue identity has no body in its source library`, 'CFB physical pin map', 'warning');
	        }
	    }
	    function collectPackageLibraryEntries(document, diagnostics) {
	        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
	        const stringsByOffset = new Map(cache?.lengthPrefixedStrings.map((entry) => [entry.offset, entry.value]));
	        const headerNamesByOffset = new Map((cache?.decodedSymbolObjects ?? []).flatMap((record) => record.kind === 'view-header' || record.kind === 'named-record' ? [[record.offset, record.name]] : []));
	        const entries = [];
	        const seen = new Set();
	        const framedOffsets = new Set();
	        for (const source of cache?.cacheSourceLibraries ?? []) {
	            const name = stringsByOffset.get(source.headerOffset) ?? headerNamesByOffset.get(source.headerOffset);
	            if (!name || !source.library.value) {
	                continue;
	            }
	            framedOffsets.add(source.headerOffset);
	            const normalizedPackageName = stripNormalViewSuffix(name);
	            const sourceLibName = source.library.value;
	            const key = packageSourceKey(normalizedPackageName, sourceLibName);
	            if (seen.has(key)) {
	                continue;
	            }
	            seen.add(key);
	            entries.push({
	                cellName: normalizedPackageName,
	                packageName: normalizedPackageName,
	                sourceLibName,
	                canonicalSourceLibName: canonicalSourceLibraryName(sourceLibName),
	                contentsViewName: normalizedPackageName,
	                partValue: normalizedPackageName,
	            });
	        }
	        if (diagnostics) {
	            for (const header of cache?.decodedSymbolObjects ?? []) {
	                if (header.kind === 'view-header' && !framedOffsets.has(header.offset)) {
	                    emitDecisionDiagnostic(diagnostics, 'package-library-unframed', `cell="${header.name}" offset=${header.offset}: no framed library; sourced physical mapping withheld`, 'CFB physical pin map', 'warning');
	                }
	            }
	        }
	        return entries;
	    }
	    function buildMarkerSourceLibraryCatalog(document) {
	        const stringCatalog = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
	        const resolved = new Map();
	        if (stringCatalog?.kind !== 'library' || !stringCatalog.stringCatalog) {
	            return resolved;
	        }
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol') {
	                    continue;
	                }
	                const source = stringCatalog.stringCatalog[object.marker];
	                if (isSourceLibraryPath(source)) {
	                    resolved.set(object, source);
	                }
	            }
	        }
	        return resolved;
	    }
	    function createPhysicalPinDiagnosticState(diagnostics) {
	        return {
	            diagnostics,
	            emitted: new Set(),
	            emittedCountByReason: new Map(),
	            suppressedCountByReason: new Map(),
	            messagePrefixByReason: new Map(),
	        };
	    }
	    function emitDecisionDiagnostic(state, reason, detail, messagePrefix, severity) {
	        state.diagnostics.push({ severity, message: `${messagePrefix} [${reason}] ${detail}` });
	    }
	    function emitBudgetedDiagnostic(state, dedupeScope, reason, detail, messagePrefix, severity) {
	        const key = `${dedupeScope}\0${reason}`;
	        if (state.emitted.has(key)) {
	            return;
	        }
	        state.emitted.add(key);
	        state.messagePrefixByReason.set(reason, messagePrefix);
	        const emittedCount = state.emittedCountByReason.get(reason) ?? 0;
	        state.emittedCountByReason.set(reason, emittedCount + 1);
	        if (emittedCount < PHYSICAL_PIN_DIAGNOSTIC_LIMIT_PER_REASON) {
	            state.diagnostics.push({ severity, message: `${messagePrefix} [${reason}] ${detail}` });
	            return;
	        }
	        state.suppressedCountByReason.set(reason, (state.suppressedCountByReason.get(reason) ?? 0) + 1);
	    }
	    function reportUndecodedSymbolPinFlagBits(document, diagnostics) {
	        for (const stream of document.streams) {
	            for (const record of stream.decodedSymbolObjects ?? []) {
	                if (record.kind !== 'symbol-pin' || record.displayFlags === undefined) {
	                    continue;
	                }
	                const unknownBits = record.displayFlags & ~OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.ORCAD_DSN_CFB_KNOWN_SYMBOL_PIN_DISPLAY_FLAGS;
	                if (unknownBits === 0) {
	                    continue;
	                }
	                emitPhysicalPinDiagnostic(diagnostics, record.name, 'pin-flags-unknown-bit', `pin="${record.name}" stream="${stream.path}" bits=0x${unknownBits.toString(16).padStart(4, '0')}`);
	            }
	        }
	    }
	    function reportUndecodedSymbolPinFields(document, diagnostics) {
	        for (const stream of document.streams) {
	            for (const record of stream.decodedSymbolObjects ?? []) {
	                if (record.kind !== 'symbol-pin') {
	                    continue;
	                }
	                if (record.displayFlags === undefined) {
	                    emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${record.name}`, 'pin-display-flags-undecoded', `pin="${record.name}" stream="${stream.path}"`, 'CFB symbol pin', 'warning');
	                }
	                if (record.pinType === undefined) {
	                    emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${record.name}`, 'pin-type-undecoded', `pin="${record.name}" stream="${stream.path}"`, 'CFB symbol pin', 'warning');
	                }
	            }
	        }
	    }
	    function emitPhysicalPinDiagnostic(state, cellName, reason, detail, sourceLibName, severity = 'warning') {
	        const strippedCell = stripNormalViewSuffix(cellName);
	        emitBudgetedDiagnostic(state, `${strippedCell}\0${canonicalSourceLibraryName(sourceLibName)}`, reason, detail, 'CFB physical pin map', severity);
	    }
	    function recoverMissingPlacedPartReferences(document, cacheCatalog, placedPartCacheIdentityCatalog, diagnostics) {
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
	                    continue;
	                }
	                const displayedValue = object.displayProperties?.find((property) => property.propertyName === 'Value' && property.displayedValue !== undefined)?.displayedValue ?? object.partValue;
	                const hasUndecodedPartReference = object.displayProperties?.some((property) => property.propertyName === 'Part Reference' && property.displayedValue === undefined);
	                const recorded = recordedPlacementReference(object, [object.reference]);
	                if (recorded.recorded) {
	                    if (hasUndecodedPartReference === true && displayedValue !== undefined) {
	                        emitDecisionDiagnostic(diagnostics, 'reference-recovery-withheld', `stream=${JSON.stringify(stream.path)} dbId=${object.dbId} source=value candidate=${JSON.stringify(displayedValue)} recorded=${JSON.stringify(recorded.reference)}: recorded page evidence takes precedence`, 'CFB reference', 'info');
	                    }
	                    continue;
	                }
	                const catalogValue = object.resolvedMetadata?.userProperties?.find((property) => property.name === 'Value')?.value;
	                const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
	                if (cacheIdentity === null) {
	                    continue;
	                }
	                const packageName = cacheIdentity?.packageName ?? object.resolvedMetadata?.pkgName ?? stripNormalViewSuffix(object.graphicName);
	                const referencePrefix = referencePrefixForPlacedPackage(document, cacheCatalog, object, packageName, cacheIdentity);
	                const matchesReferencePrefix = displayedValue !== undefined && referencePrefix !== undefined && displayedValue.startsWith(referencePrefix);
	                const packageNamePrefix = nonEmpty(leadingLettersOfPackageName(packageName));
	                const matchesPackageNamePrefix = displayedValue !== undefined &&
	                    packageNamePrefix !== undefined &&
	                    displayedValue.startsWith(packageNamePrefix);
	                if (hasUndecodedPartReference !== true ||
	                    displayedValue === undefined ||
	                    displayedValue.endsWith('?') ||
	                    catalogValue === undefined ||
	                    !(matchesReferencePrefix || (displayedValue === catalogValue && matchesPackageNamePrefix))) {
	                    continue;
	                }
	                object.reference = displayedValue;
	                emitPhysicalPinDiagnostic(diagnostics, packageName, 'reference-recovered', `dbId=${object.dbId} source=value`, cacheIdentity?.sourceLibName, 'info');
	            }
	        }
	    }
	    function referencePrefixForPlacedPackage(document, cacheCatalog, object, packageName, cacheIdentity) {
	        if (cacheIdentity) {
	            return nonEmpty(cacheIdentity.catalogEntry.referencePrefix ?? '');
	        }
	        const prefixes = new Set(catalogEntriesForGraphicName(cacheCatalog, object.graphicName)
	            .filter((entry) => entry.packageName === packageName)
	            .map((entry) => entry.referencePrefix ?? '')
	            .filter((prefix) => prefix.length > 0));
	        for (const stream of document.streams) {
	            if (stream.parsed?.kind !== 'package' || stripNormalViewSuffix(stream.parsed.packageName) !== packageName) {
	                continue;
	            }
	            const prefix = stream.decodedSymbolObjects?.find(isPartValue)?.referencePrefix;
	            if (prefix) {
	                prefixes.add(prefix);
	            }
	        }
	        if (prefixes.size === 1) {
	            return [...prefixes][0];
	        }
	        if (prefixes.size > 1) {
	            return undefined;
	        }
	        return undefined;
	    }
	    function finalizePhysicalPinDiagnostics(state) {
	        for (const [reason, count] of state.suppressedCountByReason) {
	            state.diagnostics?.push({
	                severity: 'warning',
	                message: `${state.messagePrefixByReason.get(reason) ?? 'CFB physical pin map'} [suppressed] reason=${reason} count=${count}`,
	            });
	        }
	    }
	    function buildPhysicalPinNumberCatalogs(document, cacheCatalog, physicalPinDiagnostics) {
	        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
	        const stringEntries = cache?.lengthPrefixedStrings ?? [];
	        const strings = stringEntries.map((entry) => entry.value);
	        const stringOffsets = stringEntries.map((entry) => entry.offset);
	        const catalog = new Map();
	        const numberingStylesBySection = new Map();
	        const numberingStylesByHeaderPartName = new Map();
	        const sectionObservations = new Map();
	        const profiles = buildPhysicalPinArityProfiles(document, cacheCatalog, physicalPinDiagnostics);
	        const acceptedIdentities = new Set();
	        const addNumbers = (profile, numbers) => {
	            if (numbers.length === 0) {
	                return;
	            }
	            const key = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
	            const existing = catalog.get(key) ?? [];
	            if (!existing.some((candidate) => arraysEqual(candidate.pinNumbers, numbers) && arraysEqual(candidate.pinIdentity, profile.pinIdentity))) {
	                existing.push({
	                    pinNumbers: numbers,
	                    pinIdentity: profile.pinIdentity,
	                    source: PhysicalPadRowSource.CatalogScan,
	                });
	                catalog.set(key, existing);
	            }
	        };
	        const addAcceptedIdentity = (profile) => {
	            acceptedIdentities.add(physicalPadIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName, profile.arity));
	        };
	        const rows = [];
	        for (let index = 0; index < strings.length; index++) {
	            const row = cacheCatalogRowAt(strings, index, stringOffsets);
	            if (row) {
	                rows.push(row);
	                continue;
	            }
	            if (isCacheCatalogHeaderCandidateAt(strings, index) && !hasCacheCatalogFieldDiscriminatorAt(strings, index)) {
	                const cellName = stripNormalViewSuffix(strings[index] ?? '');
	                emitPhysicalPinDiagnostic(physicalPinDiagnostics, cellName, 'header-unrecognized', `cell="${cellName}" field="${strings[index + 1] ?? ''}" offset=${stringOffsets[index] ?? index}`);
	            }
	        }
	        const recognizedCatalogCells = new Set([
	            ...rows.map((row) => row.cellName),
	            ...strings.flatMap((value) => (/\.Normal$/i.test(value) ? [stripNormalViewSuffix(value)] : [])),
	        ]);
	        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
	            const row = rows[rowIndex];
	            const rowEnd = rows
	                .slice(rowIndex + 1)
	                .find((candidate) => candidate.headerIndex >= (row.bodyStartIndex ?? row.headerIndex + 1))?.headerIndex ??
	                strings.length;
	            if (row.bodyStartIndex === undefined) {
	                emitPhysicalPinDiagnostic(physicalPinDiagnostics, row.cellName, 'header-no-body', `cell="${row.cellName}" offset=${row.headerOffset}`);
	                continue;
	            }
	            const rowProfileResolution = profilesForPhysicalPinRow(profiles, row);
	            const rowProfiles = rowProfileResolution.profiles;
	            if (rowProfiles.length === 0) {
	                emitPhysicalPinDiagnostic(physicalPinDiagnostics, row.cellName, rowProfileResolution.libraryPathAmbiguous ? 'library-path-ambiguous' : 'unresolved-row-key', `cell="${row.cellName}" source="${row.sourceLibName}" offset=${row.headerOffset}`, row.sourceLibName);
	                continue;
	            }
	            const sections = physicalPinSectionsForRow(strings, row, rowEnd, rowProfiles);
	            const rowSectionCounts = new Map();
	            for (const section of sections) {
	                for (const profile of section.profiles) {
	                    const identity = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
	                    const observation = sectionObservations.get(identity) ?? {
	                        cellName: profile.cellName,
	                        packageName: profile.packageName,
	                        canonicalSourceLibName: profile.canonicalSourceLibName,
	                        markers: new Set(),
	                        rowSectionCounts: new Set(),
	                    };
	                    if (section.marker) {
	                        observation.markers.add(section.marker);
	                    }
	                    sectionObservations.set(identity, observation);
	                    rowSectionCounts.set(identity, (rowSectionCounts.get(identity) ?? 0) + 1);
	                }
	            }
	            for (const [identity, count] of rowSectionCounts) {
	                sectionObservations.get(identity)?.rowSectionCounts.add(count);
	            }
	            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
	                const section = sections[sectionIndex];
	                const sectionEnd = sections[sectionIndex + 1]?.headerIndex ?? rowEnd;
	                const firstBodyToken = strings[section.padStartIndex];
	                if (firstBodyToken !== undefined && stripNormalViewSuffix(firstBodyToken) === row.cellName) {
	                    emitPhysicalPinDiagnostic(physicalPinDiagnostics, row.cellName, 'body-self-name', `cell="${row.cellName}" offset=${row.headerOffset}`);
	                    continue;
	                }
	                for (const profile of section.profiles) {
	                    if (!section.marker && physicalPinRootHasProfileFanout(profile, section.profiles)) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'alias-conflict', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                        continue;
	                    }
	                    if (physicalPinProfileHasAliasConflict(profile, section.profiles)) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'alias-conflict', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                        continue;
	                    }
	                    if (physicalPinProfileHasSameArityConflict(profile, section.profiles)) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'same-library-view-conflict', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                        continue;
	                    }
	                    const available = Math.max(0, sectionEnd - section.padStartIndex);
	                    if (available < profile.arity) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'row-underflow', `cell="${profile.cellName}" expected=${profile.arity} found=${available} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                        continue;
	                    }
	                    const numbers = strings.slice(section.padStartIndex, section.padStartIndex + profile.arity);
	                    const vocabularyTokens = numbers.filter((number) => {
	                        const token = stripNormalViewSuffix(number);
	                        return (!isNumericString(number) &&
	                            !/^[A-Z]$/i.test(number) &&
	                            terminalPhysicalPadFromPinName(number) === undefined &&
	                            token !== row.cellName &&
	                            recognizedCatalogCells.has(token));
	                    });
	                    const vocabularyMatches = vocabularyTokens.length;
	                    if (vocabularyMatches >= 1) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'body-vocabulary-suspect', `cell="${profile.cellName}" matches=${vocabularyMatches} tokens="${vocabularyTokens.join(',')}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                        continue;
	                    }
	                    addNumbers(profile, numbers);
	                    addAcceptedIdentity(profile);
	                    const recoveredProfile = recoverExpandedPhysicalPinProfile(profile, strings.slice(section.padStartIndex, sectionEnd), profiles);
	                    if (recoveredProfile !== undefined) {
	                        addNumbers(recoveredProfile.profile, recoveredProfile.pinNumbers);
	                        addAcceptedIdentity(recoveredProfile.profile);
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, recoveredProfile.profile.cellName, 'row-underflow-recovered', `cell="${recoveredProfile.profile.cellName}" expected=${recoveredProfile.profile.arity} found=${profile.arity} recovered=${recoveredProfile.profile.arity - profile.arity} offset=${row.headerOffset}`, recoveredProfile.profile.canonicalSourceLibName, 'info');
	                    }
	                    if (section.marker) {
	                        const style = alphabeticNumberingForSectionMarker(section.marker);
	                        const sectionKey = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
	                        if (!numberingStylesBySection.has(sectionKey)) {
	                            numberingStylesBySection.set(sectionKey, style);
	                        }
	                        const headerKey = packageSourceKey(row.cellName, profile.canonicalSourceLibName);
	                        if (!numberingStylesByHeaderPartName.has(headerKey)) {
	                            numberingStylesByHeaderPartName.set(headerKey, style);
	                        }
	                    }
	                    if (available > profile.arity) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'trailing-junk', `cell="${profile.cellName}" count=${available - profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                    }
	                    if (sectionEnd === strings.length) {
	                        emitPhysicalPinDiagnostic(physicalPinDiagnostics, profile.cellName, 'terminal-prefix-unverified', `cell="${profile.cellName}" arity=${profile.arity} offset=${row.headerOffset}`, profile.canonicalSourceLibName);
	                    }
	                }
	            }
	        }
	        const directMapsByIdentity = new Map();
	        const framedOffsets = new Set();
	        for (const source of cache?.cacheSourceLibraries ?? []) {
	            for (const section of source.physicalSections ?? []) {
	                framedOffsets.add(section.offset);
	                const cellName = stripNormalViewSuffix(section.cellName);
	                const matchingProfiles = profiles.filter((profile) => profile.cellName === cellName &&
	                    profile.canonicalSourceLibName === canonicalSourceLibraryName(source.library.value) &&
	                    profile.arity === section.pinNumbers.length &&
	                    !profile.hasSameArityConflict);
	                if (matchingProfiles.length !== 1) {
	                    emitDecisionDiagnostic(physicalPinDiagnostics, 'physical-section-identity-withheld', `cell=${JSON.stringify(cellName)} source=${JSON.stringify(source.library.value)} offset=${section.offset}: framed section has no unique matching electrical interface`, 'CFB physical pin map', 'warning');
	                    continue;
	                }
	                const profile = matchingProfiles[0];
	                const key = cacheIdentityKey(profile.cellName, profile.packageName, profile.canonicalSourceLibName);
	                const observation = sectionObservations.get(key) ?? {
	                    cellName: profile.cellName,
	                    packageName: profile.packageName,
	                    canonicalSourceLibName: profile.canonicalSourceLibName,
	                    markers: new Set(),
	                    rowSectionCounts: new Set(),
	                };
	                if (section.selector !== '') {
	                    observation.markers.add(section.selector);
	                }
	                observation.rowSectionCounts.add((source.physicalSections ?? []).filter((candidate) => stripNormalViewSuffix(candidate.cellName) === cellName)
	                    .length);
	                sectionObservations.set(key, observation);
	                const maps = directMapsByIdentity.get(key) ?? [];
	                const pinNumbers = section.pinNumbers.map((number, position) => number.length > 0 ? number : profile.pinIdentity[position]);
	                if (!maps.some((candidate) => candidate.sectionSelector === section.selector && arraysEqual(candidate.pinNumbers, pinNumbers))) {
	                    maps.push({
	                        pinNumbers,
	                        pinIdentity: profile.pinIdentity,
	                        source: PhysicalPadRowSource.FramedRecord,
	                        sectionSelector: section.selector,
	                    });
	                    directMapsByIdentity.set(key, maps);
	                }
	                addAcceptedIdentity(profile);
	            }
	        }
	        for (const pinMap of cache?.decodedSymbolObjects?.filter(isPinNumberMap) ?? []) {
	            if (!framedOffsets.has(pinMap.offset)) {
	                emitDecisionDiagnostic(physicalPinDiagnostics, 'physical-section-source-unframed', `cell=${JSON.stringify(pinMap.packageName)} offset=${pinMap.offset}: no owning physical catalogue frame`, 'CFB physical pin map', 'warning');
	            }
	        }
	        for (const [identity, maps] of directMapsByIdentity) {
	            const existingMaps = catalog.get(identity) ?? [];
	            const existingNumbers = existingMaps.map(physicalPinNumbersForCandidate);
	            const hasDisjointSectionSet = existingNumbers.length > 1 && arePinNumberMapsPairwiseDisjoint(existingNumbers);
	            catalog.set(identity, hasDisjointSectionSet ? existingMaps : maps);
	        }
	        return {
	            pinNumbers: catalog,
	            numberingStyles: { bySection: numberingStylesBySection, byHeaderPartName: numberingStylesByHeaderPartName },
	            padPresence: { acceptedIdentities },
	            sectionCounts: resolveSectionCounts(sectionObservations, physicalPinDiagnostics),
	        };
	    }
	    function recoverExpandedPhysicalPinProfile(profile, observedNumbers, profiles) {
	        const candidates = profiles.filter((candidate) => {
	            const missingCount = candidate.arity - profile.arity;
	            return (candidate.cellName === profile.cellName &&
	                candidate.packageName === profile.packageName &&
	                candidate.canonicalSourceLibName !== profile.canonicalSourceLibName &&
	                candidate.arity === observedNumbers.length &&
	                missingCount >= 1 &&
	                missingCount <= 5 &&
	                profile.pinIdentity.every((pinName) => candidate.pinIdentity.includes(pinName)));
	        });
	        const recoveries = candidates.flatMap((candidate) => {
	            const recoveredNumbers = candidate.pinIdentity.map(terminalPhysicalPadFromPinName);
	            if (recoveredNumbers.some((number) => number === undefined)) {
	                return [];
	            }
	            const pinNumbers = recoveredNumbers.filter((number) => number !== undefined);
	            const uniqueNumbers = new Set(pinNumbers.map((number) => number.toUpperCase()));
	            const observedIdentity = new Set(observedNumbers.map((number) => number.toUpperCase()));
	            const pinIdentity = new Set(candidate.pinIdentity.map((number) => number.toUpperCase()));
	            const observedPads = new Set(observedNumbers.map(terminalPhysicalPadFromPinName).filter((number) => number !== undefined));
	            const corroboratedByNames = setContentsEqual(observedIdentity, pinIdentity);
	            const corroboratedByPads = observedPads.size === observedNumbers.length && setContentsEqual(observedPads, uniqueNumbers);
	            return uniqueNumbers.size === candidate.arity && (corroboratedByNames || corroboratedByPads) ?
	                [{ profile: candidate, pinNumbers }]
	                : [];
	        });
	        return recoveries.length === 1 ? recoveries[0] : undefined;
	    }
	    function terminalPhysicalPadFromPinName(pinName) {
	        return /(?:^|_)([A-Z]{1,2}[1-9][0-9]?)$/i.exec(pinName)?.[1];
	    }
	    function setContentsEqual(left, right) {
	        return left.size === right.size && Array.from(left).every((value) => right.has(value));
	    }
	    function physicalPinRootHasProfileFanout(profile, eligibleProfiles) {
	        return (eligibleProfiles.filter((candidate) => candidate.arity === profile.arity && arraysEqual(candidate.pinIdentity, profile.pinIdentity)).length > 1);
	    }
	    function physicalPinProfileHasAliasConflict(profile, eligibleProfiles) {
	        return eligibleProfiles.some((candidate) => candidate.cellName === profile.cellName &&
	            candidate.arity === profile.arity &&
	            candidate.packageName !== profile.packageName);
	    }
	    function physicalPinProfileHasSameArityConflict(profile, eligibleProfiles) {
	        return (profile.hasSameArityConflict ||
	            eligibleProfiles.some((candidate) => candidate.arity === profile.arity && !arraysEqual(candidate.pinIdentity, profile.pinIdentity)));
	    }
	    function physicalPadPresenceAt(catalog, keys, arity, position) {
	        const cellName = keys[0];
	        if (cellName === undefined) {
	            return 'unknown';
	        }
	        const packageName = keys[1] ?? cellName;
	        const sourceLibName = keys[2] ?? '';
	        if (!catalog.acceptedIdentities.has(physicalPadIdentityKey(cellName, packageName, sourceLibName, arity))) {
	            return 'unknown';
	        }
	        return position >= 0 && position < arity ? 'present' : 'absent';
	    }
	    function physicalPadIdentityKey(cellName, packageName, sourceLibName, arity) {
	        return `${cacheIdentityKey(cellName, packageName, sourceLibName)}\0${arity}`;
	    }
	    function buildPhysicalPinArityProfiles(document, cacheCatalog, physicalPinDiagnostics) {
	        const identities = new Map();
	        for (const group of groupRootCacheObjects(document, physicalPinDiagnostics).filter((group) => group.headerKind === 'view-header' && isXmlMaterializedPackageViewGroup(group))) {
	            const cellName = stripNormalViewSuffix(group.name);
	            const entry = catalogEntryForGraphicName(cacheCatalog, group.name, group.sourceLibName, physicalPinDiagnostics);
	            const packageName = entry?.packageName ?? cellName;
	            const sourceLibName = entry?.sourceLibName ?? group.sourceLibName ?? '';
	            const canonicalSourceLibName = canonicalSourceLibraryName(sourceLibName);
	            const pins = completeCacheSymbolPins(group).map((pin) => pin.name);
	            if (pins.length === 0) {
	                continue;
	            }
	            const key = `${cacheIdentityKey(cellName, packageName, canonicalSourceLibName)}\0${pins.length}`;
	            const profile = identities.get(key) ?? {
	                cellName,
	                packageName,
	                sourceLibName,
	                canonicalSourceLibName,
	                arity: pins.length,
	                pins: [],
	            };
	            if (!profile.pins.some((identity) => arraysEqual(identity, pins))) {
	                profile.pins.push(pins);
	            }
	            identities.set(key, profile);
	        }
	        return [...identities.values()].map((profile) => ({
	            cellName: profile.cellName,
	            packageName: profile.packageName,
	            sourceLibName: profile.sourceLibName,
	            canonicalSourceLibName: profile.canonicalSourceLibName,
	            arity: profile.arity,
	            pinIdentity: profile.pins[0],
	            hasSameArityConflict: profile.pins.length > 1,
	        }));
	    }
	    function profilesForPhysicalPinRow(profiles, row) {
	        const aliases = profiles.filter((profile) => profile.cellName === row.cellName ||
	            profile.packageName === row.cellName ||
	            (row.bodyKind === 'multipart' && isSectionOfCatalogPackage(profile.cellName, row.cellName)));
	        const rowSource = canonicalSourceLibraryName(row.sourceLibName);
	        if (rowSource) {
	            return {
	                profiles: aliases.filter((profile) => profile.canonicalSourceLibName === rowSource),
	                libraryPathAmbiguous: false,
	            };
	        }
	        const libraries = new Set(aliases.map((profile) => profile.canonicalSourceLibName));
	        return {
	            profiles: libraries.size <= 1 ? aliases : [],
	            libraryPathAmbiguous: libraries.size > 1,
	        };
	    }
	    function resolveSectionCounts(observations, diagnostics) {
	        const counts = new Map();
	        for (const [identity, observation] of observations) {
	            const markerCount = observation.markers.size;
	            const resolved = Math.max(1, markerCount);
	            counts.set(identity, resolved);
	            const statedCounts = [...observation.rowSectionCounts].sort((left, right) => left - right);
	            if (statedCounts.length > 1 || (markerCount > 0 && !observation.rowSectionCounts.has(markerCount))) {
	                emitPhysicalPinDiagnostic(diagnostics, observation.cellName, 'section-count-conflict', `cell="${observation.cellName}" package="${observation.packageName}" rows=${statedCounts.join(',')} markers=${[...observation.markers].join(',')} resolved=${resolved}`, observation.canonicalSourceLibName);
	            }
	        }
	        return counts;
	    }
	    function physicalPinSectionsForRow(strings, row, rowEnd, rowProfiles) {
	        const bodyStartIndex = row.bodyStartIndex;
	        if (bodyStartIndex === undefined) {
	            return [];
	        }
	        const sections = [
	            {
	                headerIndex: row.headerIndex,
	                padStartIndex: bodyStartIndex,
	                profiles: rowProfiles,
	                ...((isMultipartSectionMarker(strings[bodyStartIndex - 2]) &&
	                    stripNormalViewSuffix(strings[bodyStartIndex - 1] ?? '') === row.cellName) ?
	                    { marker: strings[bodyStartIndex - 2] }
	                    : {}),
	            },
	        ];
	        for (let index = bodyStartIndex; index + 1 < rowEnd; index++) {
	            const marker = strings[index];
	            if (!isMultipartSectionMarker(marker)) {
	                continue;
	            }
	            const sectionName = stripNormalViewSuffix(strings[index + 1]);
	            const sectionProfiles = rowProfiles.filter((profile) => profile.cellName === sectionName &&
	                (profile.packageName === row.cellName || isSectionOfCatalogPackage(sectionName, row.cellName)));
	            if (sectionProfiles.length === 0) {
	                continue;
	            }
	            sections.push({ headerIndex: index, padStartIndex: index + 2, marker, profiles: sectionProfiles });
	        }
	        if (sections.length > 1) {
	            return sections[0].marker ? sections : sections.slice(1);
	        }
	        return row.bodyKind === 'root' ? sections : [];
	    }
	    function alphabeticNumberingForSectionMarker(marker) {
	        return /^[A-Z]$/.test(marker) ? 1 : 2;
	    }
	    function sectionIndexMatchesDesignator(designator, index) {
	        if (designator === undefined || !Number.isInteger(index) || index < 0) {
	            return false;
	        }
	        if (/^[A-D]$/.test(designator)) {
	            return designator.charCodeAt(0) - 'A'.charCodeAt(0) === index;
	        }
	        const numbered = String(index + 1);
	        return designator === numbered || designator === `-${numbered}`;
	    }
	    function resolveSectionNumberingStyle(catalog, pkgName, sourceLibName) {
	        const exact = catalog.bySection.get(cacheIdentityKey(pkgName, pkgName, sourceLibName));
	        if (exact !== undefined) {
	            return exact;
	        }
	        const exactHeader = catalog.byHeaderPartName.get(packageSourceKey(pkgName, sourceLibName));
	        if (exactHeader !== undefined) {
	            return exactHeader;
	        }
	        const canonicalSource = canonicalSourceLibraryName(sourceLibName);
	        const compatibleStyles = [];
	        for (const [key, style] of catalog.byHeaderPartName) {
	            const separator = key.indexOf('\0');
	            const source = separator >= 0 ? key.slice(0, separator) : '';
	            const partName = separator >= 0 ? key.slice(separator + 1) : key;
	            if ((canonicalSource && source !== canonicalSource) || !isSectionOfCatalogPackage(pkgName, partName)) {
	                continue;
	            }
	            compatibleStyles.push(style);
	        }
	        const uniqueStyles = Array.from(new Set(compatibleStyles));
	        return uniqueStyles.length === 1 ? uniqueStyles[0] : undefined;
	    }
	    function pinNumberMapsForPackage(catalog, sectionCounts, cellName, packageName, sourceLibName, pins, diagnostics) {
	        const normalizedPackageName = stripNormalViewSuffix(packageName);
	        const pinCount = pins.length;
	        const identity = cacheIdentityKey(cellName, normalizedPackageName, sourceLibName);
	        const sectionCount = Math.max(1, sectionCounts.get(identity) ?? 1);
	        const undecoded = {
	            outcome: pinCount > 0 ? PhysicalPadOutcome.PadsUndecoded : PhysicalPadOutcome.NoPins,
	            sectionCount,
	        };
	        const candidates = catalog.get(identity)?.filter((candidate) => candidate.pinNumbers.length === pinCount) ?? [];
	        const pinNames = pins.map((pin) => pin.name);
	        const orderedCandidates = candidates.filter((candidate) => arraysEqual(candidate.pinIdentity, pinNames));
	        if (sectionCount > 1 &&
	            orderedCandidates.length > 0 &&
	            orderedCandidates.every((candidate) => candidate.sectionSelector)) {
	            const maps = [];
	            const covered = new Set();
	            for (let index = 0; index < sectionCount; index++) {
	                const rows = orderedCandidates.filter((candidate) => sectionIndexMatchesDesignator(candidate.sectionSelector, index));
	                if (rows.length === 0 || rows.some((row) => !arraysEqual(row.pinNumbers, rows[0].pinNumbers))) {
	                    emitDecisionDiagnostic(diagnostics, 'physical-section-map-withheld', `cell=${JSON.stringify(cellName)} sectionIndex=${index}: framed selectors are missing or disagree`, 'CFB physical pin map', 'warning');
	                    return undecoded;
	                }
	                rows.forEach((row) => covered.add(row));
	                maps.push(rows[0].pinNumbers);
	            }
	            if (covered.size === orderedCandidates.length) {
	                return { outcome: PhysicalPadOutcome.Decoded, pinNumberMaps: maps, sectionCount };
	            }
	            emitDecisionDiagnostic(diagnostics, 'physical-section-map-withheld', `cell=${JSON.stringify(cellName)}: framed selectors are outside the declared section range`, 'CFB physical pin map', 'warning');
	            return undecoded;
	        }
	        if (orderedCandidates.length > 0) {
	            const orderedMaps = orderedCandidates.map(physicalPinNumbersForCandidate);
	            if (orderedMaps.length > 1 && !arePinNumberMapsPairwiseDisjoint(orderedMaps)) {
	                emitPhysicalPinDiagnostic(diagnostics, cellName, 'pad-order-ambiguous', `cell="${cellName}" package="${normalizedPackageName}" arity=${pinCount} candidates=${orderedMaps.length}`, sourceLibName);
	                return undecoded;
	            }
	            return { outcome: PhysicalPadOutcome.Decoded, pinNumberMaps: orderedMaps, sectionCount };
	        }
	        if (candidates.length > 0) {
	            emitPhysicalPinDiagnostic(diagnostics, cellName, 'pad-order-ambiguous', `cell="${cellName}" package="${normalizedPackageName}" arity=${pinCount} candidates=${candidates.length}`, sourceLibName);
	        }
	        return undecoded;
	    }
	    function physicalPinNumbersForCandidate(candidate) {
	        if (candidate.source === PhysicalPadRowSource.CatalogScan &&
	            candidate.pinNumbers.length === 1 &&
	            candidate.pinIdentity[0] === '1' &&
	            candidate.pinNumbers[0] !== '1') {
	            return candidate.pinIdentity;
	        }
	        return candidate.pinNumbers;
	    }
	    function buildLibraryInstanceCatalog(document) {
	        const libraryStream = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
	        if (libraryStream?.kind !== 'library') {
	            return { entries: [] };
	        }
	        const entries = [];
	        for (const record of libraryStream.componentCandidateRecords) {
	            const instanceMatch = /^INS(\d+)$/.exec(record.name);
	            if (!instanceMatch) {
	                continue;
	            }
	            entries.push({
	                record,
	                instanceNumber: Number(instanceMatch[1]),
	                packageKeys: libraryInstanceCatalogPackageKeys(record),
	            });
	        }
	        entries.sort((left, right) => left.instanceNumber - right.instanceNumber);
	        return { entries };
	    }
	    function libraryInstanceCatalogPackageKeys(record) {
	        const packageKeys = [];
	        for (const value of record.values) {
	            const normalized = normalizeSheetReferenceToken(stripNormalViewSuffix(pathBasename(value)));
	            if (normalized.length < 5) {
	                continue;
	            }
	            packageKeys.push({ normalized, compact: normalized.replace(/PORT/g, '') });
	        }
	        return packageKeys;
	    }
	    function buildInstanceSourceLibraryCatalog(document, diagnostics) {
	        const library = document.streams.find((stream) => stream.path === 'Root Entry/Library')?.parsed;
	        if (library?.kind === 'library') {
	            for (const record of library.componentCandidateRecords) {
	                emitDecisionDiagnostic(diagnostics, 'library-instance-source-unframed', `instance=${JSON.stringify(record.name)} offset=${record.offset}: interned strings do not state an instance-to-library association`, 'CFB source identity', 'warning');
	            }
	        }
	        return new Map();
	    }
	    function withholdHierarchyAdoption(diagnostics, trust, stream, view, id, arm, branchOffset, occurrenceId, value = OrcadDsnCfbOccurrenceOwnership_1.HierarchyAdoptionValue.Reference) {
	        const reason = (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyAdoptionVeto)(trust, stream, view, id, branchOffset, occurrenceId, value);
	        if (reason === undefined) {
	            return false;
	        }
	        emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream)}, view=${JSON.stringify(view)}, dbId=${id}, arm=${arm}: ${reason}`, 'CFB hierarchy', 'warning');
	        return true;
	    }
	    function withholdUnownedHierarchyAdoption(diagnostics, stream, id, arm) {
	        emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream)}, dbId=${id}, arm=${arm}: counted owner is unknown`, 'CFB hierarchy', 'warning');
	    }
	    function buildHierarchyReferenceLookup(document, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document), diagnostics = createPhysicalPinDiagnosticState([])) {
	        const references = new Map();
	        for (const key of occurrenceTrust.branchReferenceConflicts) {
	            emitDecisionDiagnostic(diagnostics, 'hierarchy-reference-conflict', `${key}: duplicate or disagreeing path-local rows; reference withheld`, 'CFB hierarchy occurrence', 'warning');
	        }
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'hierarchy') {
	                continue;
	            }
	            for (const entry of parsed.entries) {
	                const candidate = (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyReferenceCandidate)(entry);
	                if (candidate === undefined) {
	                    if (entry.parentBranchOffset === undefined &&
	                        entry.referenceStringOffset !== undefined &&
	                        entry.refInstId !== undefined) {
	                        emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream.path)}, offset=${entry.offset}, dbId=${entry.refInstId}: framed annotation has no eligible reference`, 'CFB hierarchy', 'warning');
	                    }
	                    continue;
	                }
	                if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, candidate.viewName, candidate.refInstId, 'reference-entry', candidate.branchOffset, candidate.occurrenceId)) {
	                    continue;
	                }
	                if (candidate.branchOffset === undefined) {
	                    withholdUnownedHierarchyAdoption(diagnostics, stream.path, candidate.refInstId, 'reference-entry');
	                    continue;
	                }
	                references.set(JSON.stringify([stream.path, candidate.branchOffset, candidate.refInstId, candidate.occurrenceId]), { ...candidate, streamPath: stream.path });
	            }
	            for (const string of stream.lengthPrefixedStrings) {
	                if (parsed.entries.some((entry) => entry.referenceStringOffset === string.offset)) {
	                    continue;
	                }
	                const refInstId = unsignedHierarchyId(string.precedingInt32Le16);
	                if (!isPlausibleHierarchyRefInstId(refInstId) || !isHierarchyOccurrenceReferenceName(string.value)) {
	                    continue;
	                }
	                withholdUnownedHierarchyAdoption(diagnostics, stream.path, refInstId, 'reference-string');
	            }
	        }
	        return references;
	    }
	    function rootHierarchyPhysicalNetNames(document, viewName, diagnostics) {
	        const names = [];
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'hierarchy' || parsed.viewName.toUpperCase() !== viewName.toUpperCase()) {
	                continue;
	            }
	            const stored = parsed.physicalNetNames ?? [];
	            names.push(...stored);
	            if (stored.length > 0) {
	                diagnostics.diagnostics.push({
	                    severity: 'warning',
	                    message: `CFB physical net name [wire-provenance-unresolved] view="${parsed.viewName}" records=${stored.length}`,
	                    streamPath: stream.path,
	                });
	            }
	        }
	        return names;
	    }
	    function calibrateRecordedPlacedPorts(document) {
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (!isPlacedSymbolRole('part-inst')(object)) {
	                    continue;
	                }
	                applyRecordedPortCalibrationToMissingCachePins(object, object.materializedPortInstances ?? []);
	            }
	        }
	    }
	    function reportHierarchyRowBelowThreshold(diagnostics, viewName, string, refInstId) {
	        const occurrenceId = unsignedHierarchyId(string.precedingInt32Le20) ?? 0;
	        if (!isDecodedHierarchyId(refInstId) || refInstId >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD) {
	            return;
	        }
	        if (occurrenceId !== 0 && occurrenceId >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD) {
	            return;
	        }
	        diagnostics.diagnostics?.push({
	            severity: 'info',
	            message: `CFB hierarchy occurrence [row-id-below-threshold] view="${viewName}" reference="${string.value}" refInstId=${refInstId} occurrenceId=${occurrenceId}: read as a counted-string neighbour rather than an occurrence row`,
	        });
	    }
	    function buildHierarchyOccurrenceCatalog(document, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
	        const byBranchAndRefInstId = buildHierarchyReferenceLookup(document, occurrenceTrust, diagnostics);
	        const references = [...byBranchAndRefInstId.values()];
	        const branches = [];
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'hierarchy') {
	                continue;
	            }
	            for (const branch of parsed.ownershipBranches ?? []) {
	                if (branch.viewName === undefined || branch.state !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Resolved) {
	                    continue;
	                }
	                const parents = branch.offset === 0 ?
	                    [undefined]
	                    : parsed.entries.filter((entry) => entry.offset + 8 === branch.offset && entry.parentBranchOffset !== undefined);
	                if (parents.length === 0) {
	                    emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(stream.path)}, offset=${branch.offset}, view=${JSON.stringify(branch.viewName)}: resolved occurrence branch has no counted parent`, 'CFB hierarchy', 'warning');
	                    continue;
	                }
	                for (const parent of parents) {
	                    if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, branch.viewName, parent?.dbId, 'occurrence-branch', branch.offset)) {
	                        continue;
	                    }
	                    branches.push({
	                        streamPath: stream.path,
	                        viewName: branch.viewName,
	                        offset: branch.offset,
	                        parentOffset: parent?.parentBranchOffset,
	                        refInstId: parent?.dbId,
	                        occurrenceId: parent?.recordId,
	                    });
	                }
	            }
	            for (const string of stream.lengthPrefixedStrings) {
	                if (parsed.entries.some((entry) => entry.referenceStringOffset === string.offset)) {
	                    continue;
	                }
	                const refInstId = unsignedHierarchyId(string.precedingInt32Le16);
	                if (refInstId === undefined || !isHierarchyOccurrenceReferenceName(string.value)) {
	                    continue;
	                }
	                if (!isPlausibleHierarchyOccurrenceRow(string)) {
	                    reportHierarchyRowBelowThreshold(diagnostics, parsed.viewName, string, refInstId);
	                    continue;
	                }
	                withholdUnownedHierarchyAdoption(diagnostics, stream.path, refInstId, 'occurrence-catalogue');
	            }
	        }
	        references.sort((left, right) => left.offset - right.offset);
	        const referencesByBranch = new Map();
	        for (const reference of references) {
	            const key = JSON.stringify([reference.streamPath, reference.branchOffset]);
	            const byPlacement = referencesByBranch.get(key) ??
	                new Map();
	            const rows = byPlacement.get(reference.refInstId);
	            if (rows === undefined) {
	                byPlacement.set(reference.refInstId, [reference]);
	            }
	            else {
	                rows.push(reference);
	            }
	            referencesByBranch.set(key, byPlacement);
	        }
	        const childrenByParentAndPlacement = new Map();
	        const rootsByView = new Map();
	        for (const branch of branches) {
	            const key = JSON.stringify([branch.streamPath, branch.parentOffset, String(branch.refInstId)]);
	            const children = childrenByParentAndPlacement.get(key) ?? [];
	            children.push(branch);
	            childrenByParentAndPlacement.set(key, children);
	            if (branch.offset === 0) {
	                const view = branch.viewName.toUpperCase();
	                const roots = rootsByView.get(view) ?? [];
	                roots.push(branch);
	                rootsByView.set(view, roots);
	            }
	        }
	        return {
	            trust: occurrenceTrust,
	            branches,
	            references,
	            byBranchAndRefInstId,
	            referencesByBranch,
	            childrenByParentAndPlacement,
	            rootsByView,
	        };
	    }
	    function agreeingHierarchyAnnotation(rows, diagnostics, key) {
	        const identities = new Set(rows.map((row) => row.occurrenceId));
	        if (identities.size > 1) {
	            emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `${key}: occurrence identity is ambiguous; agreed reference retained without an identity`, 'CFB hierarchy occurrence', 'warning');
	        }
	        const { refInstId, reference } = rows[0];
	        return { refInstId, reference, occurrenceId: identities.size === 1 ? [...identities][0] : undefined };
	    }
	    function buildOccurrenceNetSystemIdCatalog(document, diagnostics = createPhysicalPinDiagnosticState([]), trust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
	        const viewNames = new Set(document.schematicViews.map((view) => view.viewName.toUpperCase()));
	        const catalog = new Map();
	        const claims = new Map();
	        for (const stream of document.streams) {
	            if (stream.parsed?.kind !== 'hierarchy' || !stream.parsed.records) {
	                continue;
	            }
	            let sheetDbId;
	            let sheetView;
	            for (const record of stream.parsed.records) {
	                if (!record.name) {
	                    continue;
	                }
	                if (record.nameOffset !== undefined && record.nameOffset >= 8 && viewNames.has(record.name.toUpperCase())) {
	                    sheetDbId = record.dbId;
	                    sheetView = record.name;
	                    continue;
	                }
	                if (sheetDbId === undefined) {
	                    continue;
	                }
	                if (withholdHierarchyAdoption(diagnostics, trust, stream.path, sheetView, sheetDbId, 'net-system-id')) {
	                    continue;
	                }
	                let byName = claims.get(sheetDbId);
	                if (!byName) {
	                    byName = new Map();
	                    claims.set(sheetDbId, byName);
	                }
	                const key = record.name.toUpperCase();
	                const values = byName.get(key) ?? new Set();
	                values.add(record.recordId);
	                byName.set(key, values);
	            }
	        }
	        for (const [id, byName] of claims) {
	            const resolved = new Map();
	            for (const [name, values] of byName) {
	                if (values.size === 1) {
	                    for (const value of values) {
	                        resolved.set(name, value);
	                    }
	                }
	                else {
	                    emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `net-system-id dbId=${id}, net=${JSON.stringify(name)}: conflicting recorded ids`, 'CFB hierarchy', 'warning');
	                }
	            }
	            if (resolved.size > 0) {
	                catalog.set(id, resolved);
	            }
	        }
	        return catalog;
	    }
	    function sheetSymbolCatalogNames(candidate) {
	        if (candidate.entry.contentsViewName === UNNAMED_CONTENTS_VIEW) {
	            return [candidate.packageName];
	        }
	        return [candidate.packageName, candidate.entry.contentsViewName];
	    }
	    function contradictedSheetSymbolNames(candidates, diagnostics) {
	        const stated = new Map();
	        const contradicted = new Set();
	        for (const candidate of candidates) {
	            for (const name of sheetSymbolCatalogNames(candidate)) {
	                const key = stripNormalViewSuffix(name);
	                const known = stated.get(key);
	                if (!known) {
	                    stated.set(key, candidate);
	                    continue;
	                }
	                if (contradicted.has(key) ||
	                    !statesDifferentSheetSymbol({ packageName: key, ...known.entry }, candidate.entry)) {
	                    continue;
	                }
	                contradicted.add(key);
	                diagnostics.report('sheet-metadata-conflict', `the cells describing "${key}" state different sheet-symbol metadata, so neither is read as this symbol's`, candidate.streamPath, candidate.offset);
	            }
	        }
	        return contradicted;
	    }
	    function buildSheetSymbolPackageCatalog(document, sourceFileName, diagnostics) {
	        const sheetAliasDiagnostics = new LibrarySheetAliasDiagnosticBudget(diagnostics);
	        const catalog = new Map();
	        const addEntry = (packageName, entry) => {
	            const normalizedPackageName = stripNormalViewSuffix(packageName);
	            if (!isPlausibleCatalogPackageName(normalizedPackageName)) {
	                return;
	            }
	            const metadata = { packageName: normalizedPackageName, ...entry };
	            const merged = mergeSheetSymbolPackageMetadata(catalog.get(normalizedPackageName), metadata);
	            catalog.set(normalizedPackageName, merged);
	        };
	        const candidates = [];
	        for (const stream of document.streams) {
	            const packageName = /^Root Entry\/Packages\/([^/]+)$/.exec(stream.path)?.[1];
	            if (!packageName) {
	                continue;
	            }
	            for (const record of stream.sheetSymbolMetadataRecords ?? []) {
	                const contentsLibName = canonicalChildDsnPath(record.contentsLibName);
	                if (!contentsLibName ||
	                    !isDsnSourcePath(contentsLibName) ||
	                    isSameDsnFileName(contentsLibName, sourceFileName)) {
	                    continue;
	                }
	                const cellName = record.cellViewName ? stripNormalViewSuffix(record.cellViewName) : packageName;
	                const entry = sheetSymbolEntryFromRecord(record, contentsLibName, stream.path, cellName, sheetAliasDiagnostics);
	                if (!entry) {
	                    continue;
	                }
	                candidates.push({ packageName: cellName, entry, streamPath: stream.path, offset: record.offset });
	            }
	        }
	        for (const stream of document.streams.filter((stream) => stream.path === 'Root Entry/Cache')) {
	            for (const record of stream.sheetSymbolMetadataRecords ?? []) {
	                const contentsLibName = canonicalChildDsnPath(record.contentsLibName);
	                if (!contentsLibName ||
	                    !isDsnSourcePath(contentsLibName) ||
	                    isSameDsnFileName(contentsLibName, sourceFileName)) {
	                    continue;
	                }
	                if (!record.cellViewName) {
	                    sheetAliasDiagnostics.report('sheet-record-uncelled', `the record naming "${contentsLibName}" sits in no cell, so it states no symbol's sheet metadata`, stream.path, record.offset);
	                    continue;
	                }
	                const packageName = stripNormalViewSuffix(record.cellViewName);
	                const entry = sheetSymbolEntryFromRecord(record, contentsLibName, stream.path, packageName, sheetAliasDiagnostics);
	                if (!entry) {
	                    continue;
	                }
	                candidates.push({ packageName, entry, streamPath: stream.path, offset: record.offset });
	            }
	        }
	        const contradicted = contradictedSheetSymbolNames(candidates, sheetAliasDiagnostics);
	        for (const candidate of candidates) {
	            if (contradicted.has(stripNormalViewSuffix(candidate.packageName))) {
	                continue;
	            }
	            for (const name of sheetSymbolCatalogNames(candidate)) {
	                if (contradicted.has(stripNormalViewSuffix(name))) {
	                    continue;
	                }
	                addEntry(name, candidate.entry);
	            }
	        }
	        for (const stream of document.streams.filter((stream) => stream.path === 'Root Entry/Library')) {
	            const rows = orderedStreamTextRows(stream);
	            const reportedRecordStarts = new Set();
	            for (let index = 0; index < rows.length; index++) {
	                const contentsLibName = canonicalChildDsnPath(rows[index].value);
	                if (!isDsnSourcePath(contentsLibName) || isSameDsnFileName(contentsLibName, sourceFileName)) {
	                    continue;
	                }
	                const contentsViewName = dsnViewNameFromPath(contentsLibName);
	                if (!contentsViewName) {
	                    continue;
	                }
	                const record = libraryRecordBounds(rows, index);
	                const reportRecord = !reportedRecordStarts.has(record.start);
	                if (record.truncated) {
	                    if (reportRecord) {
	                        reportedRecordStarts.add(record.start);
	                        sheetAliasDiagnostics.report('library-record-truncated-tail', `child design "${contentsViewName}" sits in a block the stream ends without an instance marker, so it states no record and contributes no sheet symbol metadata`, stream.path, rows[record.start]?.offset);
	                    }
	                    continue;
	                }
	                const { partValue, unanchored } = librarySheetPartValueInRecord(rows, index, record);
	                const entry = { contentsLibName, contentsViewName, ...(partValue ? { partValue } : {}) };
	                addEntry(contentsViewName, entry);
	                if (partValue) {
	                    addEntry(partValue, entry);
	                }
	                if (unanchored && reportRecord) {
	                    sheetAliasDiagnostics.report('sheet-part-value-unanchored', `child design "${contentsViewName}": "${unanchored}" precedes its path in a slot no symbol library opens, so it is read as another column of the record rather than this child's part value`, stream.path, rows[record.start]?.offset);
	                }
	                const candidates = librarySheetAliasCandidatesInRecord(rows, index, record, contentsViewName);
	                for (const alias of candidates.aliases) {
	                    addEntry(alias, { contentsLibName, contentsViewName: alias, ...(partValue ? { partValue } : {}) });
	                }
	                if (!reportRecord) {
	                    continue;
	                }
	                reportedRecordStarts.add(record.start);
	                if (candidates.childViewNames.length > 1) {
	                    sheetAliasDiagnostics.report('sheet-alias-ambiguous', `Library record names ${candidates.childViewNames.length} child designs (${quoteList(candidates.childViewNames)}); none of its other rows is read as a child view alias`, stream.path, rows[record.start]?.offset);
	                    continue;
	                }
	                if (candidates.rejected.length > 0) {
	                    sheetAliasDiagnostics.report('sheet-alias-unsupported', `child design "${contentsViewName}": ${quoteList(candidates.rejected)} share its Library record but name no spelling of it`, stream.path, rows[record.start]?.offset);
	                }
	            }
	        }
	        sheetAliasDiagnostics.summarize();
	        return catalog;
	    }
	    const LIBRARY_SHEET_ALIAS_DIAGNOSTIC_LIMIT_PER_REASON = 25;
	    const LIBRARY_SHEET_ALIAS_NAMES_PER_DIAGNOSTIC = 8;
	    class LibrarySheetAliasDiagnosticBudget {
	        constructor(diagnostics) {
	            this.diagnostics = diagnostics;
	            this.emittedCountByReason = new Map();
	            this.suppressedCountByReasonAndStream = new Map();
	        }
	        report(reason, detail, streamPath, offset) {
	            const emitted = this.emittedCountByReason.get(reason) ?? 0;
	            this.emittedCountByReason.set(reason, emitted + 1);
	            if (emitted >= LIBRARY_SHEET_ALIAS_DIAGNOSTIC_LIMIT_PER_REASON) {
	                const key = `${reason} ${streamPath}`;
	                this.suppressedCountByReasonAndStream.set(key, (this.suppressedCountByReasonAndStream.get(key) ?? 0) + 1);
	                return;
	            }
	            this.diagnostics?.push({
	                severity: 'info',
	                message: `CFB DSN sheet symbol [${reason}] ${detail}`,
	                streamPath,
	                ...(offset === undefined ? {} : { offset }),
	            });
	        }
	        summarize() {
	            for (const [key, suppressed] of this.suppressedCountByReasonAndStream) {
	                const [reason, streamPath] = key.split(' ');
	                this.diagnostics?.push({
	                    severity: 'info',
	                    message: `CFB DSN sheet symbol [${reason}] ${suppressed} further Library record(s) were not reported`,
	                    streamPath,
	                });
	            }
	        }
	    }
	    function quoteList(values) {
	        const listed = values.slice(0, LIBRARY_SHEET_ALIAS_NAMES_PER_DIAGNOSTIC);
	        const quoted = listed.map((value) => `"${value}"`).join(', ');
	        return values.length > listed.length ? `${quoted} and ${values.length - listed.length} more` : quoted;
	    }
	    function libraryRecordBounds(rows, index) {
	        if (index < 0 || index >= rows.length) {
	            return { start: index, end: index };
	        }
	        if (isLibraryInstanceMarker(rows[index].value)) {
	            let runStart = index;
	            while (runStart > 0 && isLibraryInstanceMarker(rows[runStart - 1].value)) {
	                runStart--;
	            }
	            let blockStart = runStart;
	            while (blockStart > 0 && !isLibraryInstanceMarker(rows[blockStart - 1].value)) {
	                blockStart--;
	            }
	            return { start: blockStart, end: runStart };
	        }
	        let start = index;
	        while (start > 0 && !isLibraryInstanceMarker(rows[start - 1].value)) {
	            start--;
	        }
	        let end = index + 1;
	        while (end < rows.length && !isLibraryInstanceMarker(rows[end].value)) {
	            end++;
	        }
	        if (end === rows.length) {
	            return { start, end, truncated: true };
	        }
	        return { start, end };
	    }
	    function isLibraryInstanceMarker(value) {
	        return OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.isLibraryInstanceMarker(value);
	    }
	    function sheetSymbolEntryFromRecord(record, contentsLibName, streamPath, packageName, diagnostics) {
	        const fieldState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.OrCadDsnCfbMetadataFieldState;
	        const malformed = [record.view, record.symbolClass, record.partValue].some((field) => field.state === fieldState.malformed);
	        if (malformed) {
	            diagnostics.report('sheet-metadata-malformed', `the record for child design "${contentsLibName}" does not lay its fields out as counted strings, so it states no sheet-symbol metadata`, streamPath, record.offset);
	            return undefined;
	        }
	        if (record.view.state === fieldState.empty) {
	            diagnostics.report('sheet-view-unresolved', `package "${packageName}" states an empty view name for child design "${contentsLibName}", so it names no view of that design`, streamPath, record.offset);
	            return { contentsLibName, contentsViewName: UNNAMED_CONTENTS_VIEW };
	        }
	        const contentsViewName = record.view.value;
	        if (contentsViewName === undefined) {
	            return undefined;
	        }
	        const referencePrefix = record.symbolClass.state === fieldState.stated && isSheetReferencePrefix(record.symbolClass.value) ?
	            record.symbolClass.value
	            : undefined;
	        const partValue = record.partValue.state === fieldState.stated ? record.partValue.value : undefined;
	        return {
	            contentsLibName,
	            contentsViewName,
	            ...(referencePrefix ? { referencePrefix } : {}),
	            ...(partValue ? { partValue } : {}),
	        };
	    }
	    function statesDifferentSheetSymbol(known, incoming) {
	        if (known.contentsViewName === UNNAMED_CONTENTS_VIEW || incoming.contentsViewName === UNNAMED_CONTENTS_VIEW) {
	            return false;
	        }
	        return (!isSameDsnFileName(known.contentsLibName, incoming.contentsLibName) ||
	            normalizeSheetReferenceToken(known.contentsViewName) !== normalizeSheetReferenceToken(incoming.contentsViewName));
	    }
	    function librarySheetPartValueInRecord(rows, dsnIndex, record) {
	        const previousValue = dsnIndex - 1 >= record.start ? rows[dsnIndex - 1]?.value : undefined;
	        if (!previousValue) {
	            return {};
	        }
	        const anchorValue = dsnIndex - 2 >= record.start ? rows[dsnIndex - 2]?.value : undefined;
	        if (anchorValue !== undefined && isSourceLibraryPath(anchorValue)) {
	            return { partValue: previousValue };
	        }
	        return isPlausibleLibrarySheetPartValue(previousValue) ? { unanchored: previousValue } : {};
	    }
	    function childDesignsNamedInRecord(rows, record) {
	        const childViewNames = [];
	        for (let index = record.start; index < record.end; index++) {
	            const rowValue = rows[index].value;
	            const childViewName = isDsnSourcePath(rowValue) ? dsnViewNameFromPath(rowValue) : undefined;
	            if (childViewName && !childViewNames.some((seen) => isSameChildDesign(seen, childViewName))) {
	                childViewNames.push(childViewName);
	            }
	        }
	        return childViewNames;
	    }
	    function namesSameChildDesign(value, pathViewName) {
	        return areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(value), normalizeSheetReferenceToken(pathViewName));
	    }
	    function isSameChildDesign(left, right) {
	        if (isDsnSourcePath(left) && isDsnSourcePath(right)) {
	            return resolvedChildDesignPath(left) === resolvedChildDesignPath(right);
	        }
	        return childDesignName(left) === childDesignName(right);
	    }
	    function resolvedChildDesignPath(value) {
	        return (canonicalChildDsnPath(value) ?? value).toUpperCase();
	    }
	    function childDesignName(value) {
	        const bare = isDsnSourcePath(value) ? pathBasename(value) : value;
	        return bare
	            .trim()
	            .replace(/\.(?:dsn|xml)$/i, '')
	            .replace(/\.(?:Normal|IEEE|Convert)$/i, '')
	            .replace(/[\s.]+$/, '')
	            .replace(/[\s_]+/g, '_')
	            .toUpperCase();
	    }
	    function librarySheetAliasCandidatesInRecord(rows, dsnIndex, record, pathViewName) {
	        const childViewNames = childDesignsNamedInRecord(rows, record);
	        if (childViewNames.length > 1) {
	            return { aliases: [], rejected: [], childViewNames };
	        }
	        const aliases = [];
	        const rejected = [];
	        for (let index = record.start; index < record.end; index++) {
	            if (index === dsnIndex) {
	                continue;
	            }
	            const value = rows[index].value;
	            if (!isPlausibleLibrarySheetViewCandidate(value, pathViewName)) {
	                continue;
	            }
	            if (namesSameChildDesign(value, pathViewName)) {
	                aliases.push(value);
	            }
	            else {
	                rejected.push(value);
	            }
	        }
	        return { aliases, rejected, childViewNames };
	    }
	    function isPlausibleLibrarySheetViewCandidate(value, pathViewName) {
	        return isPlausibleLibrarySheetPartValue(value) && !areSheetReferenceKeysCompatible(value, pathViewName);
	    }
	    function mergeSheetSymbolPackageMetadata(existing, incoming) {
	        if (!existing) {
	            return incoming;
	        }
	        const keepExistingView = Boolean(existing.referencePrefix && !incoming.referencePrefix);
	        const existingPathMatchesView = childDsnPathMatchesView(existing.contentsLibName, existing.contentsViewName);
	        const incomingPathMatchesView = childDsnPathMatchesView(incoming.contentsLibName, incoming.contentsViewName);
	        return {
	            packageName: existing.packageName,
	            contentsLibName: incomingPathMatchesView && !existingPathMatchesView ? incoming.contentsLibName : existing.contentsLibName,
	            contentsViewName: keepExistingView ? existing.contentsViewName : incoming.contentsViewName,
	            referencePrefix: existing.referencePrefix ?? incoming.referencePrefix,
	            partValue: preferredSheetPartValue(existing, incoming),
	        };
	    }
	    function childDsnPathMatchesView(contentsLibName, contentsViewName) {
	        const fileName = pathBasename(contentsLibName).replace(/\.dsn$/i, '');
	        return areSheetReferenceKeysCompatible(fileName, contentsViewName);
	    }
	    function preferredSheetPartValue(existing, incoming) {
	        if (incoming.partValue && !areSheetReferenceKeysCompatible(incoming.partValue, incoming.contentsViewName)) {
	            return incoming.partValue;
	        }
	        return existing.partValue ?? incoming.partValue;
	    }
	    function dsnViewNameFromPath(contentsLibName) {
	        return contentsLibName
	            .split(/[\\/]/)
	            .pop()
	            ?.replace(/\.dsn$/i, '');
	    }
	    function isPlausibleLibrarySheetPartValue(value) {
	        return value.length > 0;
	    }
	    function orderedStreamTextRows(stream) {
	        const rows = [
	            ...stream.lengthPrefixedStrings.map((entry) => ({ offset: entry.offset, value: entry.value })),
	            ...(stream.asciiRuns ?? []).map((entry) => ({ offset: entry.offset, value: entry.value })),
	        ].sort((left, right) => left.offset - right.offset || left.value.localeCompare(right.value));
	        const values = [];
	        for (const row of rows) {
	            const previous = values[values.length - 1];
	            if (previous && previous.value === row.value && row.offset - previous.offset <= 4) {
	                continue;
	            }
	            values.push(row);
	        }
	        return values;
	    }
	    function canonicalChildDsnPath(value) {
	        if (!value || !isDsnSourcePath(value)) {
	            return value;
	        }
	        return value.replace(/\//g, '\\');
	    }
	    function isSameDsnFileName(contentsLibName, sourceFileName) {
	        if (!sourceFileName) {
	            return false;
	        }
	        return pathBasename(contentsLibName).toUpperCase() === pathBasename(sourceFileName).toUpperCase();
	    }
	    function pathBasename(value) {
	        return value.replace(/\\/g, '/').split('/').pop() ?? value;
	    }
	    function isPlausibleHierarchySheetReference(value) {
	        return Boolean(value && /^[A-Za-z][A-Za-z0-9_.-]*\??$/.test(value) && value !== 'X');
	    }
	    function buildHierarchySheetReferenceCatalog(document, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ?? (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
	        const references = [];
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'hierarchy') {
	                continue;
	            }
	            for (const entry of parsed.entries) {
	                const contentsViewName = entry.contentsViewName;
	                const claimedId = entry.refInstId ?? entry.dbId;
	                if (!contentsViewName ||
	                    isGenericSheetViewName(contentsViewName) ||
	                    contentsViewName.toUpperCase() === parsed.viewName.toUpperCase()) {
	                    continue;
	                }
	                const isOccurrenceAnnotation = Boolean(!isPlausibleHierarchySheetReference(entry.reference) &&
	                    entry.dbId !== undefined &&
	                    entry.occurrenceId === undefined &&
	                    entry.refInstId === undefined);
	                if (withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream.path, contentsViewName, claimedId, 'sheet-reference')) {
	                    continue;
	                }
	                if (!isPlausibleHierarchySheetReference(entry.reference) && !isOccurrenceAnnotation) {
	                    continue;
	                }
	                references.push({
	                    contentsViewName,
	                    parentViewName: entry.parentViewName,
	                    reference: entry.reference ?? '?',
	                    offset: entry.offset,
	                    ...(entry.dbId !== undefined ? { dbId: entry.dbId } : {}),
	                    ...(isOccurrenceAnnotation ? { isOccurrenceAnnotation: true } : {}),
	                });
	            }
	        }
	        references.sort((left, right) => left.offset - right.offset);
	        const annotationsByDbId = new Map();
	        for (const reference of references) {
	            if (!reference.isOccurrenceAnnotation || reference.dbId === undefined) {
	                continue;
	            }
	            const entries = annotationsByDbId.get(reference.dbId) ?? [];
	            entries.push(reference);
	            annotationsByDbId.set(reference.dbId, entries);
	        }
	        for (const [dbId, entries] of annotationsByDbId) {
	            const views = new Set(entries.map((entry) => entry.contentsViewName.toUpperCase()));
	            if (views.size > 1) {
	                emitBudgetedDiagnostic(diagnostics, String(dbId), 'hierarchy-annotation-ambiguous', `dbId=${dbId} views=${Array.from(views).sort().join(',')}`, 'CFB hierarchy', 'warning');
	            }
	        }
	        return { references, usedIndexes: new Set(), claimCounts: new Map() };
	    }
	    function buildLibraryStringCatalog(document) {
	        return document.streams.find((stream) => stream.path === 'Root Entry/Library')?.lengthPrefixedStrings ?? [];
	    }
	    function buildCisSchematicInfoCatalog(document) {
	        const catalog = new Map();
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'cis-schematic-info') {
	                continue;
	            }
	            const byRecordId = new Map();
	            for (const record of parsed.records) {
	                const exportedProperties = record.properties.filter((property) => property.value.length > 0 || isXmlExportedEmptyCisProperty(property.name));
	                if (record.recordId <= 0 || exportedProperties.length === 0) {
	                    continue;
	                }
	                byRecordId.set(record.recordId, exportedProperties.map((property) => ({ Name: property.name, Value: property.value })));
	            }
	            if (byRecordId.size > 0) {
	                catalog.set(cisSchematicInfoKey(parsed.viewName, parsed.pageName), byRecordId);
	            }
	        }
	        return catalog;
	    }
	    function cisSchematicInfoKey(viewName, pageName) {
	        return `${viewName}/${pageName}`;
	    }
	    function isXmlExportedEmptyCisProperty(name) {
	        return [
	            'CURRENT_RATING',
	            'DIELECTRIC',
	            'FPLIST',
	            'FREQUENCY',
	            'ICA_MFR_NAME',
	            'ICA_PARTNUM',
	            'MATERIAL',
	            'NS',
	            'Part Number',
	            'RATING',
	            'ROOM',
	            'TOLERANCE',
	            'VOLTAGE',
	        ].includes(name);
	    }
	    function libraryEntryForDbId(libraryInstanceCatalog, dbId) {
	        const firstAtOrAfter = firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, dbId);
	        return firstAtOrAfter === 0 ? undefined : libraryInstanceCatalog.entries[firstAtOrAfter - 1];
	    }
	    function libraryEntryForPlacedPackage(libraryInstanceCatalog, dbId, pkgName) {
	        return (libraryEntryForCompatiblePackage(libraryInstanceCatalog, dbId, pkgName) ??
	            libraryEntryForDbId(libraryInstanceCatalog, dbId));
	    }
	    function libraryEntryForCompatiblePackage(libraryInstanceCatalog, dbId, pkgName) {
	        const normalizedPackageKey = normalizeSheetReferenceToken(stripNormalViewSuffix(pkgName));
	        const packageKey = {
	            normalized: normalizedPackageKey,
	            compact: normalizedPackageKey.replace(/PORT/g, ''),
	        };
	        if (!packageKey.normalized || packageKey.normalized.length < 5) {
	            return undefined;
	        }
	        const firstInWindow = firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, dbId - 1000);
	        const firstAtOrAfter = firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, dbId);
	        let best;
	        for (let index = firstInWindow; index < firstAtOrAfter; index++) {
	            const entry = libraryInstanceCatalog.entries[index];
	            if (!libraryEntryMatchesPackage(entry, packageKey)) {
	                continue;
	            }
	            best = entry;
	        }
	        return best;
	    }
	    function firstLibraryInstanceCatalogEntryAtOrAfter(libraryInstanceCatalog, bound) {
	        let lower = 0;
	        let upper = libraryInstanceCatalog.entries.length;
	        while (lower < upper) {
	            const index = Math.floor((lower + upper) / 2);
	            const value = libraryInstanceCatalog.entries[index].instanceNumber;
	            if (value >= bound) {
	                upper = index;
	            }
	            else {
	                lower = index + 1;
	            }
	        }
	        return lower;
	    }
	    function libraryEntryMatchesPackage(entry, packageKey) {
	        return entry.packageKeys.some((valueKey) => {
	            if (sheetReferenceKeysMatchDirectly(packageKey.normalized, valueKey.normalized)) {
	                return true;
	            }
	            return compactSheetReferenceKeysMatch(packageKey.compact, valueKey.compact);
	        });
	    }
	    function packageSourceKey(packageName, sourceLibName) {
	        return `${canonicalSourceLibraryName(sourceLibName)}\0${stripNormalViewSuffix(packageName)}`;
	    }
	    function crossSourcePackageKey(packageName) {
	        return stripNormalViewSuffix(packageName);
	    }
	    function canonicalSourceLibraryName(sourceLibName) {
	        const normalized = (sourceLibName ?? '').trim().replace(/\\/g, '/');
	        if (!normalized) {
	            return '';
	        }
	        const prefix = normalized.startsWith('//') ? '//'
	            : normalized.startsWith('/') ? '/'
	                : '';
	        const segments = [];
	        for (const segment of normalized.split('/')) {
	            if (!segment || segment === '.') {
	                continue;
	            }
	            if (segment === '..') {
	                if (segments.length > 0 && segments[segments.length - 1] !== '..') {
	                    segments.pop();
	                }
	                else if (!prefix) {
	                    segments.push(segment);
	                }
	                continue;
	            }
	            segments.push(segment);
	        }
	        return `${prefix}${segments.join('/')}`.toLowerCase();
	    }
	    function cacheIdentityKey(cellName, packageName, sourceLibName) {
	        return `${stripNormalViewSuffix(cellName)}\0${stripNormalViewSuffix(packageName)}\0${canonicalSourceLibraryName(sourceLibName)}`;
	    }
	    function cacheCellLibraryKey(cellName, sourceLibName) {
	        return `${stripNormalViewSuffix(cellName)}\0${canonicalSourceLibraryName(sourceLibName)}`;
	    }
	    function buildCacheCatalog(document, diagnostics) {
	        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
	        const stringEntries = cache?.lengthPrefixedStrings ?? [];
	        const strings = stringEntries.map((entry) => entry.value);
	        const stringOffsets = stringEntries.map((entry) => entry.offset);
	        const catalog = new Map();
	        const structurallyRegisteredCells = new Set();
	        const framedEntries = collectPackageLibraryEntries(document, diagnostics);
	        const framedIdentities = new Set(framedEntries.map((entry) => cacheCellLibraryKey(entry.cellName, entry.sourceLibName)));
	        for (let index = 0; index < strings.length; index++) {
	            const row = cacheCatalogRowAt(strings, index, stringOffsets);
	            if (!row) {
	                continue;
	            }
	            if (row.sourceLibName && !framedIdentities.has(cacheCellLibraryKey(row.cellName, row.sourceLibName))) {
	                emitDecisionDiagnostic(diagnostics, 'package-library-unframed', `cell="${row.cellName}" offset=${row.headerOffset}: no framed library; sourced physical mapping withheld`, 'CFB physical pin map', 'warning');
	                continue;
	            }
	            const entry = {
	                cellName: row.cellName,
	                packageName: row.cellName,
	                sourceLibName: row.sourceLibName,
	                canonicalSourceLibName: canonicalSourceLibraryName(row.sourceLibName),
	                ...(row.referencePrefix ? { referencePrefix: row.referencePrefix } : {}),
	                contentsViewName: row.cellName,
	                partValue: row.partValue ?? row.cellName,
	            };
	            addCacheCatalogEntry(catalog, row.cellName, entry);
	            structurallyRegisteredCells.add(cacheCellLibraryKey(row.cellName, row.sourceLibName));
	            addSectionCatalogEntries(catalog, strings, row.sectionStartIndex, entry, row.provenance === 'split');
	        }
	        addFramedLibraryCatalogEntries(catalog, framedEntries, structurallyRegisteredCells);
	        return catalog;
	    }
	    function isCacheCatalogHeaderCandidateAt(strings, index) {
	        if (index + 3 >= strings.length) {
	            return false;
	        }
	        const cellName = stripNormalViewSuffix(strings[index] ?? '');
	        const standardRepeat = stripNormalViewSuffix(strings[index + 2] ?? '') === cellName;
	        const alternateSourceRepeat = isAlternateSourceCatalogHeaderAt(strings, index);
	        const singleLibraryRepeat = isSingleLibraryCatalogHeaderAt(strings, index);
	        const nonLibrarySourceRoot = isNonLibrarySourceRootCatalogHeaderAt(strings, index);
	        return (isPlausibleCatalogPackageName(cellName) &&
	            (standardRepeat || alternateSourceRepeat || singleLibraryRepeat || nonLibrarySourceRoot));
	    }
	    function hasCacheCatalogFieldDiscriminatorAt(strings, index) {
	        const field = strings[index + 1] ?? '';
	        if (isNumericString(field) || (isCacheCatalogMarkerToken(field) && !/^[A-Za-z]$/.test(field))) {
	            return true;
	        }
	        return ((isSourceLibraryPath(field) && isSourceLibraryPath(strings[index + 3])) ||
	            isAlternateSourceCatalogHeaderAt(strings, index) ||
	            isSingleLibraryCatalogHeaderAt(strings, index) ||
	            bareLetterRefdesMultipartBodyStartAt(strings, index) !== undefined ||
	            isBareLetterRootCatalogHeaderAt(strings, index) ||
	            isNonLibrarySourceRootCatalogHeaderAt(strings, index));
	    }
	    function isSingleLibraryCatalogHeaderAt(strings, index) {
	        const footprint = strings[index + 3] ?? '';
	        return (isSourceLibraryPath(strings[index + 1]) &&
	            isReferencePrefix(strings[index + 2]) &&
	            !isSourceLibraryPath(footprint) &&
	            !footprint.startsWith('$') &&
	            !isNumericString(footprint) &&
	            !isCacheCatalogMarkerToken(footprint) &&
	            strings[index + 4] === strings[index]);
	    }
	    function isAlternateSourceCatalogHeaderAt(strings, index) {
	        return (index > 0 &&
	            isSourceLibraryPath(strings[index - 1]) &&
	            isSourceLibraryPath(strings[index + 1]) &&
	            canonicalSourceLibraryName(strings[index - 1]) === canonicalSourceLibraryName(strings[index + 1]) &&
	            isReferencePrefix(strings[index + 2]) &&
	            strings[index + 3] === strings[index]);
	    }
	    function bareLetterRefdesMultipartBodyStartAt(strings, index) {
	        const cellName = stripNormalViewSuffix(strings[index] ?? '');
	        const field = strings[index + 1] ?? '';
	        const referencePrefix = strings[index + 3];
	        if (!isPlausibleCatalogPackageName(cellName) ||
	            stripNormalViewSuffix(strings[index + 2] ?? '') !== cellName ||
	            !/^[A-Za-z]$/.test(field) ||
	            isSourceLibraryPath(referencePrefix) ||
	            !isReferencePrefix(referencePrefix)) {
	            return undefined;
	        }
	        let cursor = index + 4;
	        const footprint = strings[cursor];
	        if (!isMultipartSectionMarker(footprint) &&
	            !isSourceLibraryPath(footprint) &&
	            !(footprint ?? '').startsWith('$') &&
	            !isNumericString(footprint) &&
	            !isCacheCatalogMarkerToken(footprint)) {
	            cursor++;
	        }
	        const sectionName = stripNormalViewSuffix(strings[cursor + 1] ?? '');
	        return (isMultipartSectionMarker(strings[cursor]) &&
	            sectionName.length > cellName.length &&
	            sectionName.startsWith(cellName)) ?
	            cursor
	            : undefined;
	    }
	    function isBareLetterRootCatalogHeaderAt(strings, index) {
	        const field = strings[index + 1] ?? '';
	        const rawCellName = strings[index] ?? '';
	        const cursor = index + 4;
	        return (/^[A-Za-z]$/.test(field) &&
	            isSourceLibraryPath(strings[index + 3]) &&
	            strings.slice(cursor + 1, Math.min(cursor + 4, strings.length)).some((value) => value === rawCellName));
	    }
	    function isNonLibrarySourceRootCatalogHeaderAt(strings, index) {
	        const rawCellName = strings[index] ?? '';
	        const field = strings[index + 1] ?? '';
	        const standardFrame = /^[A-Za-z]$/.test(field) &&
	            strings[index + 2] === rawCellName &&
	            isReferencePrefix(strings[index + 3]) &&
	            (strings[index + 4] === rawCellName || strings[index + 5] === rawCellName);
	        const compactFrame = strings[index + 1] === rawCellName && isReferencePrefix(strings[index + 2]) && strings[index + 3] === rawCellName;
	        return standardFrame || compactFrame;
	    }
	    function cacheCatalogRowAt(strings, index, stringOffsets = []) {
	        if (!isCacheCatalogHeaderCandidateAt(strings, index) || !hasCacheCatalogFieldDiscriminatorAt(strings, index)) {
	            return undefined;
	        }
	        const rawCellName = strings[index] ?? '';
	        const cellName = stripNormalViewSuffix(rawCellName);
	        if (isSingleLibraryCatalogHeaderAt(strings, index)) {
	            const bodyStartIndex = index + 5;
	            return {
	                cellName,
	                sourceLibName: strings[index + 1] ?? '',
	                referencePrefix: strings[index + 2],
	                sectionStartIndex: bodyStartIndex,
	                bodyStartIndex,
	                headerIndex: index,
	                headerOffset: stringOffsets[index] ?? index,
	                bodyStartOffset: stringOffsets[bodyStartIndex] ?? bodyStartIndex,
	                bodyKind: 'root',
	                provenance: 'split',
	            };
	        }
	        const field = strings[index + 1] ?? '';
	        const counted = isNumericString(field);
	        const marker = isCacheCatalogMarkerToken(field) && !/^[A-Za-z]$/.test(field);
	        const bareLetterRefdesMultipartBodyStart = bareLetterRefdesMultipartBodyStartAt(strings, index);
	        const alternateSourceRepeat = isAlternateSourceCatalogHeaderAt(strings, index);
	        const nonLibrarySourceRoot = isNonLibrarySourceRootCatalogHeaderAt(strings, index);
	        const sourceLibCandidate = strings[alternateSourceRepeat ? index + 1 : index + 3];
	        const sourceLibName = isSourceLibraryPath(sourceLibCandidate) ? sourceLibCandidate : '';
	        const split = !counted &&
	            !marker &&
	            isSourceLibraryPath(field) &&
	            isSourceLibraryPath(sourceLibName) &&
	            stripNormalViewSuffix(strings[index + 2] ?? '') === cellName;
	        const hasRepeatedBodyAnchor = strings
	            .slice(index + 4, Math.min(index + 8, strings.length))
	            .some((value) => value === rawCellName);
	        if (!split &&
	            !isSourceLibraryPath(sourceLibName) &&
	            !hasRepeatedBodyAnchor &&
	            !nonLibrarySourceRoot &&
	            bareLetterRefdesMultipartBodyStart === undefined) {
	            return undefined;
	        }
	        let cursor = index + (alternateSourceRepeat ? 2 : 4);
	        if (isCacheCatalogMarkerToken(strings[cursor]) && !/^[A-Za-z]$/.test(strings[cursor] ?? '')) {
	            cursor++;
	        }
	        let referencePrefix;
	        let partValue;
	        let bodyStartIndex;
	        let bodyKind;
	        if (counted &&
	            strings[cursor] === rawCellName &&
	            isReferencePrefix(strings[index + 3]) &&
	            strings[cursor - 2] === rawCellName) {
	            referencePrefix = strings[index + 3];
	            bodyStartIndex = cursor + 1;
	            cursor = bodyStartIndex;
	            bodyKind = 'root';
	        }
	        else if (alternateSourceRepeat) {
	            referencePrefix = isReferencePrefix(strings[cursor]) ? strings[cursor] : undefined;
	            if (strings[cursor + 1] === rawCellName) {
	                cursor += 2;
	                bodyStartIndex = cursor;
	                bodyKind = 'root';
	            }
	        }
	        else if (split) {
	            referencePrefix = strings[cursor];
	            if (strings[cursor + 1] === rawCellName) {
	                cursor++;
	                while (strings[cursor + 1] === rawCellName) {
	                    cursor++;
	                }
	                cursor++;
	                bodyKind = 'root';
	            }
	            else if (strings[cursor + 2] === rawCellName &&
	                isReferencePrefix(strings[cursor]) &&
	                !isSourceLibraryPath(strings[cursor + 1]) &&
	                !(strings[cursor + 1] ?? '').startsWith('$') &&
	                !isNumericString(strings[cursor + 1]) &&
	                !isCacheCatalogMarkerToken(strings[cursor + 1])) {
	                let anchorIndex = cursor + 2;
	                while (strings[anchorIndex + 1] === rawCellName) {
	                    anchorIndex++;
	                }
	                cursor = anchorIndex + 1;
	                bodyKind = 'root';
	            }
	            else {
	                cursor++;
	                bodyKind = 'multipart';
	            }
	            bodyStartIndex = cursor;
	        }
	        else if (bareLetterRefdesMultipartBodyStart !== undefined) {
	            referencePrefix = strings[index + 3];
	            cursor = bareLetterRefdesMultipartBodyStart;
	            bodyStartIndex = cursor;
	            bodyKind = 'multipart';
	        }
	        else if (nonLibrarySourceRoot &&
	            strings[index + 1] === rawCellName &&
	            isReferencePrefix(strings[index + 2]) &&
	            strings[index + 3] === rawCellName) {
	            referencePrefix = strings[index + 2];
	            cursor = index + 4;
	            bodyStartIndex = cursor;
	            bodyKind = 'root';
	        }
	        else {
	            const repeatedPackageSearchStart = nonLibrarySourceRoot ? cursor : cursor + 1;
	            const repeatedPackageOffset = strings
	                .slice(repeatedPackageSearchStart, Math.min(repeatedPackageSearchStart + 3, strings.length))
	                .findIndex((value) => value === rawCellName);
	            const repeatedPackageIndex = repeatedPackageOffset < 0 ? -1 : repeatedPackageSearchStart - cursor + repeatedPackageOffset;
	            if ((counted || marker) &&
	                isMultipartSectionMarker(strings[cursor + 1] ?? '') &&
	                isSectionOfCatalogPackage(stripNormalViewSuffix(strings[cursor + 2] ?? ''), cellName)) {
	                referencePrefix = strings[cursor];
	                cursor++;
	                bodyStartIndex = cursor;
	                bodyKind = 'multipart';
	            }
	            else if (repeatedPackageIndex > 0) {
	                let anchorIndex = cursor + repeatedPackageIndex;
	                const referenceCandidate = strings[anchorIndex - 1];
	                if (nonLibrarySourceRoot && isReferencePrefix(strings[index + 3])) {
	                    referencePrefix = strings[index + 3];
	                    partValue = catalogPartValueAfterReferencePrefix(referenceCandidate, cellName);
	                }
	                else if (anchorIndex - 1 > cursor && isReferencePrefix(strings[cursor])) {
	                    referencePrefix = strings[cursor];
	                    partValue = catalogPartValueAfterReferencePrefix(referenceCandidate, cellName);
	                }
	                else if (anchorIndex > cursor && isReferencePrefix(referenceCandidate)) {
	                    referencePrefix = referenceCandidate;
	                }
	                while (strings[anchorIndex + 1] === rawCellName) {
	                    anchorIndex++;
	                }
	                cursor = anchorIndex + 1;
	                bodyStartIndex = cursor;
	                bodyKind = 'root';
	            }
	            else if (repeatedPackageIndex === 0 && nonLibrarySourceRoot) {
	                referencePrefix = isReferencePrefix(strings[index + 3]) ? strings[index + 3] : undefined;
	                while (strings[cursor + 1] === rawCellName) {
	                    cursor++;
	                }
	                cursor++;
	                bodyStartIndex = cursor;
	                bodyKind = 'root';
	            }
	            else if ((counted || marker) &&
	                isPlausibleCatalogPackageName(stripNormalViewSuffix(strings[cursor + 1] ?? '')) &&
	                !isSourceLibraryPath(strings[cursor + 1] ?? '') &&
	                isMultipartSectionMarker(strings[cursor + 2] ?? '') &&
	                isSectionOfCatalogPackage(stripNormalViewSuffix(strings[cursor + 3] ?? ''), cellName)) {
	                referencePrefix = strings[cursor];
	                cursor += 2;
	                bodyStartIndex = cursor;
	                bodyKind = 'multipart';
	            }
	            else if (isReferencePrefix(strings[cursor])) {
	                referencePrefix = strings[cursor];
	                cursor++;
	            }
	        }
	        partValue ??= catalogPartValueAfterReferencePrefix(strings[cursor], cellName);
	        return {
	            cellName,
	            sourceLibName,
	            ...(referencePrefix ? { referencePrefix } : {}),
	            ...(partValue ? { partValue } : {}),
	            sectionStartIndex: cursor,
	            ...(bodyStartIndex !== undefined ? { bodyStartIndex } : {}),
	            headerIndex: index,
	            headerOffset: stringOffsets[index] ?? index,
	            ...(bodyStartIndex !== undefined ? { bodyStartOffset: stringOffsets[bodyStartIndex] ?? bodyStartIndex } : {}),
	            ...(bodyKind ? { bodyKind } : {}),
	            provenance: bareLetterRefdesMultipartBodyStart !== undefined ? 'marker'
	                : counted ? 'counted'
	                    : marker ? 'marker'
	                        : 'split',
	        };
	    }
	    function catalogPartValueAfterReferencePrefix(value, packageName) {
	        const normalizedValue = stripNormalViewSuffix(value ?? '');
	        if (!value ||
	            isNumericString(value) ||
	            isDeviceDesignatorToken(value) ||
	            isSourceLibraryPath(value) ||
	            isCacheCatalogMarkerToken(value) ||
	            /^(?:REFDES|PART NUMBER|DESCRIPTION)$/i.test(value) ||
	            normalizedValue === packageName ||
	            isSectionOfCatalogPackage(normalizedValue, packageName)) {
	            return undefined;
	        }
	        return value;
	    }
	    function addFramedLibraryCatalogEntries(catalog, candidates, structurallyRegisteredCells) {
	        for (const candidate of candidates) {
	            if (structurallyRegisteredCells.has(cacheCellLibraryKey(candidate.cellName, candidate.sourceLibName))) {
	                continue;
	            }
	            const candidateLibrary = canonicalSourceLibraryName(candidate.sourceLibName);
	            const registeredPackageNames = new Set([
	                ...Array.from(catalog.values())
	                    .flat()
	                    .filter((entry) => entry.canonicalSourceLibName === candidateLibrary)
	                    .map((entry) => entry.packageName),
	                ...candidates
	                    .filter((entry) => canonicalSourceLibraryName(entry.sourceLibName) === candidateLibrary)
	                    .map((entry) => entry.cellName),
	            ]);
	            const parentPackageName = parentCatalogPackageName(candidate.cellName, registeredPackageNames);
	            const existing = catalogEntriesForGraphicName(catalog, candidate.cellName).find((entry) => entry.canonicalSourceLibName === candidateLibrary);
	            if (existing) {
	                if (parentPackageName && existing.packageName !== parentPackageName) {
	                    addCacheCatalogEntry(catalog, candidate.cellName, {
	                        ...existing,
	                        packageName: parentPackageName,
	                        contentsViewName: candidate.cellName,
	                    });
	                }
	                continue;
	            }
	            const packageName = parentPackageName ?? candidate.cellName;
	            const entry = {
	                cellName: candidate.cellName,
	                packageName,
	                sourceLibName: candidate.sourceLibName,
	                canonicalSourceLibName: candidateLibrary,
	                ...(candidate.referencePrefix ? { referencePrefix: candidate.referencePrefix } : {}),
	                contentsViewName: candidate.cellName,
	                partValue: candidate.cellName,
	            };
	            addCacheCatalogEntry(catalog, candidate.cellName, entry);
	        }
	    }
	    function parentCatalogPackageName(cellName, packageNames) {
	        return Array.from(packageNames)
	            .filter((packageName) => packageName !== cellName &&
	            isSectionOfCatalogPackage(cellName, packageName) &&
	            !isAlternatePackageCatalogSection(cellName, packageName))
	            .sort((left, right) => right.length - left.length || left.localeCompare(right))[0];
	    }
	    function addSectionCatalogEntries(catalog, strings, startIndex, entry, preserveSectionPackageIdentity) {
	        let pendingDeviceDesignator;
	        for (let index = startIndex; index < strings.length; index++) {
	            const value = stripNormalViewSuffix(strings[index]);
	            if (cacheCatalogRowAt(strings, index)) {
	                break;
	            }
	            if (isDeviceDesignatorToken(value)) {
	                pendingDeviceDesignator = value;
	                continue;
	            }
	            if (!isSectionOfCatalogPackage(value, entry.packageName)) {
	                continue;
	            }
	            if (value === entry.packageName) {
	                continue;
	            }
	            if (isAlternatePackageCatalogSection(value, entry.packageName)) {
	                addCacheCatalogEntry(catalog, value, {
	                    ...entry,
	                    cellName: value,
	                    packageName: value,
	                    contentsViewName: value,
	                    partValue: value,
	                    ...(pendingDeviceDesignator ? { deviceDesignator: pendingDeviceDesignator } : {}),
	                });
	                continue;
	            }
	            addCacheCatalogEntry(catalog, value, {
	                ...entry,
	                cellName: value,
	                sectionParentName: entry.packageName,
	                ...(preserveSectionPackageIdentity ?
	                    { packageName: value, contentsViewName: value, preserveSectionPackageIdentity: true }
	                    : { contentsViewName: value }),
	                ...(pendingDeviceDesignator ? { deviceDesignator: pendingDeviceDesignator } : {}),
	            });
	        }
	    }
	    function addCacheCatalogEntry(catalog, key, entry) {
	        const normalizedKey = stripNormalViewSuffix(key);
	        const existingEntries = catalog.get(normalizedKey) ?? [];
	        const sameLibrarySection = existingEntries.find((candidate) => candidate.canonicalSourceLibName === entry.canonicalSourceLibName && candidate.sectionParentName);
	        if (!entry.sectionParentName && sameLibrarySection && !sameLibrarySection.preserveSectionPackageIdentity) {
	            return;
	        }
	        if (entry.sectionParentName &&
	            entry.preserveSectionPackageIdentity &&
	            existingEntries.some((candidate) => candidate.canonicalSourceLibName === entry.canonicalSourceLibName && !candidate.sectionParentName)) {
	            return;
	        }
	        const entries = entry.sectionParentName ?
	            existingEntries.filter((candidate) => candidate.canonicalSourceLibName !== entry.canonicalSourceLibName)
	            : existingEntries.filter((candidate) => candidate.canonicalSourceLibName !== entry.canonicalSourceLibName || !candidate.sectionParentName);
	        const identity = cacheIdentityKey(entry.cellName, entry.packageName, entry.canonicalSourceLibName);
	        const existingIndex = entries.findIndex((candidate) => cacheIdentityKey(candidate.cellName, candidate.packageName, candidate.canonicalSourceLibName) === identity);
	        const existing = existingIndex >= 0 ? entries[existingIndex] : undefined;
	        const mergedEntry = (existing?.partValue &&
	            existing.partValue !== existing.packageName &&
	            (!entry.partValue || entry.partValue === entry.packageName)) ?
	            { ...entry, partValue: existing.partValue }
	            : entry;
	        const updated = existingIndex >= 0 ?
	            entries.map((candidate, index) => (index === existingIndex ? mergedEntry : candidate))
	            : [...entries, mergedEntry];
	        catalog.set(normalizedKey, updated);
	        catalog.set(`${normalizedKey}.Normal`, updated);
	        catalog.set(`${normalizedKey}.IEEE`, updated);
	    }
	    function catalogEntriesForGraphicName(catalog, graphicName) {
	        for (const key of catalogLookupKeys(graphicName)) {
	            const entries = catalog.get(key);
	            if (entries) {
	                return entries;
	            }
	        }
	        return [];
	    }
	    function catalogEntryForGraphicName(catalog, graphicName, sourceLibName, diagnostics) {
	        const entries = catalogEntriesForGraphicName(catalog, graphicName);
	        const canonicalSource = canonicalSourceLibraryName(sourceLibName);
	        const matches = entries.filter((entry) => entry.canonicalSourceLibName === canonicalSource);
	        if (matches.length === 1) {
	            return matches[0];
	        }
	        if (entries.length > 0) {
	            emitDecisionDiagnostic(diagnostics, 'package-library-association-withheld', `cell="${graphicName}" source=${JSON.stringify(sourceLibName)} candidates=${JSON.stringify(entries.map((entry) => entry.sourceLibName))}: no unique exact-source catalogue association`, 'CFB physical pin map', 'warning');
	        }
	        return undefined;
	    }
	    function resolvePlacedPartCacheIdentity(object, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalog, instanceSourceLibraries, markerSourceLibraries, diagnostics) {
	        const cellName = stripNormalViewSuffix(object.graphicName);
	        const candidates = dedupePlacedPartCacheCandidates(cellName, catalogEntriesForGraphicName(cacheCatalog, object.graphicName));
	        const identityFor = (entry, resolution, sourceLibName = entry.sourceLibName) => {
	            const cachePins = canonicalSourceLibraryName(sourceLibName) === entry.canonicalSourceLibName ?
	                cachePinsForPlacedGraphic(packageViewPinCatalog, object, cellName, entry, diagnostics)
	                : undefined;
	            return {
	                cellName,
	                packageName: entry.packageName,
	                sourceLibName,
	                canonicalSourceLibName: canonicalSourceLibraryName(sourceLibName),
	                cacheSourceLibName: entry.sourceLibName,
	                catalogEntry: entry,
	                hasDeclaredBounds: cachePins !== undefined && packageViewPinCatalog.pinsWithDeclaredBounds.has(cachePins),
	                cachePins,
	                resolution,
	            };
	        };
	        const selectLibrary = (sourceLibName, resolution) => {
	            const canonicalSource = canonicalSourceLibraryName(sourceLibName);
	            if (!canonicalSource) {
	                return undefined;
	            }
	            const matches = candidates.filter((entry) => entry.canonicalSourceLibName === canonicalSource);
	            return matches.length === 1 ? identityFor(matches[0], resolution) : undefined;
	        };
	        const markerSource = markerSourceLibraries.get(object);
	        const markerCanonical = canonicalSourceLibraryName(markerSource);
	        const explicitSource = explicitSourceLibraryForPlacedPart(object);
	        const explicitCanonical = canonicalSourceLibraryName(explicitSource);
	        const instanceSource = instanceSourceLibraries.get(object.resolvedMetadata?.name ?? '');
	        const instanceCanonical = canonicalSourceLibraryName(instanceSource);
	        const statedSources = new Set([markerCanonical, explicitCanonical, instanceCanonical].filter((source) => source.length > 0));
	        if (statedSources.size > 1) {
	            emitPhysicalPinDiagnostic(diagnostics, `${cellName}\0${object.dbId}\0${object.offset}`, 'library-source-conflict', `cell="${cellName}" marker="${markerSource ?? ''}" explicit="${explicitSource ?? ''}" instance="${instanceSource ?? ''}" reference="${object.reference ?? ''}": library withheld`, undefined, 'warning');
	            return null;
	        }
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        const markerMatches = markerCanonical ? candidates.filter((entry) => entry.canonicalSourceLibName === markerCanonical) : [];
	        if (markerMatches.length === 1) {
	            return identityFor(markerMatches[0], 'placed-library-reference');
	        }
	        if (markerCanonical &&
	            markerSource &&
	            candidates.length === 1 &&
	            candidates[0].canonicalSourceLibName.length === 0) {
	            return identityFor(candidates[0], 'placed-library-reference', markerSource);
	        }
	        if (!markerCanonical) {
	            const explicit = selectLibrary(explicitSource, 'explicit');
	            if (explicit) {
	                return explicit;
	            }
	            const associated = selectLibrary(instanceSource, 'instance-library');
	            if (associated) {
	                return associated;
	            }
	        }
	        emitPhysicalPinDiagnostic(diagnostics, cellName, 'library-source-unresolved', `cell="${cellName}" candidates=${candidates.length} reference="${object.reference ?? ''}"`, undefined, 'warning');
	        const consensusPhysicalPinNumbers = physicalPinNumberConsensus(object, cellName, candidates, physicalPinNumberCatalog);
	        if (consensusPhysicalPinNumbers) {
	            applyPhysicalPinNumberConsensus(object, consensusPhysicalPinNumbers);
	            emitPhysicalPinDiagnostic(diagnostics, cellName, 'physical-map-consensus', `cell="${cellName}" candidates=${candidates.length} reference="${object.reference ?? ''}"`, undefined, 'info');
	            return undefined;
	        }
	        emitPhysicalPinDiagnostic(diagnostics, cellName, 'library-marker-ambiguous', `cell="${cellName}" marker=${object.marker} candidates=${candidates.length}`);
	        emitPhysicalPinDiagnostic(diagnostics, cellName, 'library-indeterminate', `cell="${cellName}" candidates=${candidates.length} reference="${object.reference ?? ''}"`);
	        return undefined;
	    }
	    function physicalPinNumberConsensus(object, cellName, candidates, catalog) {
	        const arity = recordedPlacedPortArity(object);
	        if (arity === undefined || candidates.length < 2) {
	            return undefined;
	        }
	        let consensus;
	        for (const candidate of candidates) {
	            const vectors = new Map();
	            for (const map of catalog.get(cacheIdentityKey(cellName, candidate.packageName, candidate.sourceLibName)) ?? []) {
	                const vector = physicalPinNumbersForCandidate(map);
	                if (vector.length === arity) {
	                    vectors.set(JSON.stringify(vector), vector);
	                }
	            }
	            if (vectors.size !== 1) {
	                return undefined;
	            }
	            for (const vector of vectors.values()) {
	                if (consensus !== undefined && !arraysEqual(consensus, vector)) {
	                    return undefined;
	                }
	                consensus = vector;
	            }
	        }
	        return consensus;
	    }
	    function applyPhysicalPinNumberConsensus(object, pinNumbers) {
	        for (const port of object.materializedPortInstances ?? []) {
	            if (port.position !== undefined && port.position >= 0 && port.position < pinNumbers.length) {
	                port.physicalPinNumber = pinNumbers[port.position];
	            }
	        }
	    }
	    function isOrdinalPinNumberMap(pinNumbers) {
	        return pinNumbers.every((pinNumber, position) => pinNumber === String(position + 1));
	    }
	    function dedupePlacedPartCacheCandidates(cellName, candidates) {
	        const identities = new Map();
	        for (const candidate of candidates) {
	            const key = cacheIdentityKey(cellName, candidate.packageName, candidate.canonicalSourceLibName);
	            if (!identities.has(key)) {
	                identities.set(key, candidate);
	            }
	        }
	        return Array.from(identities.values());
	    }
	    function explicitSourceLibraryForPlacedPart(object) {
	        const metadata = object.resolvedMetadata;
	        if (metadata?.libName && isSourceLibraryPath(metadata.libName)) {
	            return metadata.libName;
	        }
	        if (metadata?.contentsLibName &&
	            /\.olb$/i.test(metadata.contentsLibName) &&
	            isSourceLibraryPath(metadata.contentsLibName)) {
	            return metadata.contentsLibName;
	        }
	        return undefined;
	    }
	    function placedPartGeometryMatchesCachePins(object, cachePins) {
	        const ports = object.materializedPortInstances ?? [];
	        if (ports.length === 0 || !cachePins?.length) {
	            return false;
	        }
	        return ports.every((port) => {
	            const hotptX = port.hotptX ?? port.x;
	            const hotptY = port.hotptY ?? port.y;
	            const startX = port.startX;
	            const startY = port.startY;
	            if (hotptX === undefined || hotptY === undefined || startX === undefined || startY === undefined) {
	                return false;
	            }
	            return cachePins.some((pin) => pin.hotptX === hotptX && pin.hotptY === hotptY && pin.startX === startX && pin.startY === startY);
	        });
	    }
	    function buildPlacedPartCacheIdentityCatalog(document, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalog, instanceSourceLibraries, markerSourceLibraries, diagnostics) {
	        const identities = new WeakMap();
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
	                    continue;
	                }
	                const identity = resolvePlacedPartCacheIdentity(object, cacheCatalog, packageViewPinCatalog, physicalPinNumberCatalog, instanceSourceLibraries, markerSourceLibraries, diagnostics);
	                if (identity !== undefined) {
	                    identities.set(object, identity);
	                }
	            }
	        }
	        return identities;
	    }
	    function buildPlacedViewEvidenceCatalog(document, identities) {
	        const placed = new Map();
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
	                    continue;
	                }
	                const identity = identities.get(object);
	                if (identity === null) {
	                    continue;
	                }
	                const cellName = identity?.cellName ?? stripNormalViewSuffix(object.graphicName);
	                const packageName = identity?.packageName ?? object.resolvedMetadata?.pkgName ?? cellName;
	                const sourceLibName = identity?.sourceLibName ?? object.resolvedMetadata?.libName ?? '';
	                const key = cacheIdentityKey(cellName, packageName, sourceLibName);
	                const objects = placed.get(key) ?? [];
	                objects.push(object);
	                placed.set(key, objects);
	            }
	        }
	        return placed;
	    }
	    function pagePointKey(x, y) {
	        return `${x}\0${y}`;
	    }
	    function buildSymbolSourceCatalog(document) {
	        const sources = new Map();
	        for (const [name, sourceLibNames] of buildSymbolSourceLibraryCatalog(document)) {
	            if (sourceLibNames[0]) {
	                sources.set(name, sourceLibNames[0]);
	            }
	        }
	        return sources;
	    }
	    function buildSymbolSourceLibraryCatalog(document) {
	        const cache = document.streams.find((stream) => stream.path === 'Root Entry/Cache');
	        const strings = cache?.lengthPrefixedStrings.map((entry) => entry.value) ?? [];
	        const sources = new Map();
	        const addSource = (name, sourceLibName) => {
	            if (!name || !isSourceLibraryPath(sourceLibName)) {
	                return;
	            }
	            const values = sources.get(name) ?? [];
	            if (!values.includes(sourceLibName)) {
	                values.push(sourceLibName);
	                sources.set(name, values);
	            }
	        };
	        for (let index = 0; index + 1 < strings.length; index++) {
	            const name = stripNormalViewSuffix(strings[index]);
	            const compactSource = strings[index + 1];
	            if (isSourceLibraryPath(compactSource)) {
	                addSource(name, compactSource);
	                continue;
	            }
	            const countedName = stripNormalViewSuffix(strings[index + 2] ?? '');
	            const countedSource = strings[index + 3];
	            if (isNumericString(strings[index + 1]) && countedName === name && isSourceLibraryPath(countedSource)) {
	                addSource(name, countedSource);
	            }
	        }
	        return sources;
	    }
	    function catalogLookupKeys(graphicName) {
	        const stripped = stripNormalViewSuffix(graphicName);
	        return [graphicName, stripped, `${stripped}.Normal`, `${stripped}.IEEE`];
	    }
	    function buildLivePartPackages(document, existingPackageIdentities, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, diagnostics) {
	        const packageObjects = new Map();
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
	                    continue;
	                }
	                const identity = placedPartCacheIdentityCatalog.get(object);
	                if (identity === null) {
	                    continue;
	                }
	                const catalogEntry = identity?.catalogEntry;
	                const pkgName = identity?.packageName ?? pkgNameFromGraphicName(object.graphicName);
	                const markerSource = markerSourceLibraries.get(object);
	                const sources = new Set([identity?.sourceLibName, markerSource, catalogEntry?.sourceLibName].filter((source) => source !== undefined && source.length > 0));
	                if (sources.size > 1) {
	                    emitDecisionDiagnostic(diagnostics, 'live-package-source-conflict', `package="${pkgName}" page="${stream.path}" offset=${object.offset} sources=${JSON.stringify([...sources])}: body withheld`, 'CFB physical pin map', 'warning');
	                    continue;
	                }
	                const [statedSource] = sources;
	                const sourceLibName = statedSource ?? '';
	                const packageIdentity = packageSourceKey(pkgName, sourceLibName);
	                if (!pkgName) {
	                    continue;
	                }
	                if (existingPackageIdentities.has(packageIdentity)) {
	                    continue;
	                }
	                if (sourceLibName.length > 0) {
	                    emitPhysicalPinDiagnostic(diagnostics, packageIdentity, 'placed-library-live-package-withheld', `package="${pkgName}" source="${sourceLibName}": no exact source-labelled electrical interface`, undefined, 'warning');
	                    continue;
	                }
	                const group = packageObjects.get(packageIdentity) ?? {
	                    packageNames: new Set(),
	                    catalogEntries: new Map(),
	                    objects: [],
	                };
	                group.packageNames.add(pkgName);
	                if (catalogEntry) {
	                    group.catalogEntries.set(JSON.stringify(catalogEntry), catalogEntry);
	                }
	                group.objects.push(object);
	                packageObjects.set(packageIdentity, group);
	            }
	        }
	        const packages = [];
	        for (const [packageIdentity, group] of packageObjects) {
	            if (group.packageNames.size !== 1 || group.catalogEntries.size > 1) {
	                emitDecisionDiagnostic(diagnostics, 'live-package-catalogue-conflict', `packages=${JSON.stringify([...group.packageNames])} catalogues=${group.catalogEntries.size}: body withheld`, 'CFB physical pin map', 'warning');
	                continue;
	            }
	            const [pkgName] = group.packageNames;
	            const [catalogEntry] = group.catalogEntries.values();
	            existingPackageIdentities.add(packageIdentity);
	            packages.push(buildPackageFromPlacedParts(pkgName, '', group.objects, catalogEntry, physicalPadPresenceCatalog, diagnostics));
	        }
	        return packages;
	    }
	    function buildPlacedSourcePackages(document, packages, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, diagnostics) {
	        const groups = new Map();
	        const resolved = new Map();
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                const identity = object.kind === 'placed-symbol' ? placedPartCacheIdentityCatalog.get(object) : undefined;
	                const sourceLibName = object.kind === 'placed-symbol' ? markerSourceLibraries.get(object) : undefined;
	                if (object.kind !== 'placed-symbol' ||
	                    object.role !== 'part-inst' ||
	                    identity === null ||
	                    (identity && identity.cacheSourceLibName.length > 0) ||
	                    !sourceLibName) {
	                    continue;
	                }
	                const packageName = identity?.packageName ?? pkgNameFromGraphicName(object.graphicName);
	                const cellName = stripNormalViewSuffix(object.graphicName);
	                const key = packageSourceKey(packageName, sourceLibName);
	                const group = groups.get(key) ?? {
	                    packageName,
	                    sourceLibName,
	                    cellNames: new Set(),
	                    objects: [],
	                };
	                group.cellNames.add(cellName);
	                group.objects.push(object);
	                groups.set(key, group);
	            }
	        }
	        for (const [key, group] of groups) {
	            const interfaces = new Map();
	            for (const cellName of group.cellNames) {
	                const matchesCell = (pkg) => pkg.Name === group.packageName &&
	                    pkg.LibPart.some((part) => stripNormalViewSuffix(part.CellName ?? pkg.Name) === cellName);
	                const candidates = packages.filter((pkg) => matchesCell(pkg) &&
	                    canonicalSourceLibraryName(pkg.SourceLibName) === canonicalSourceLibraryName(group.sourceLibName));
	                const normalizedCandidates = candidates
	                    .map((candidate) => placedSourcePackageElectricalInterface(candidate, cellName))
	                    .filter((candidate) => candidate !== undefined);
	                const distinct = new Map(normalizedCandidates.map((candidate) => [JSON.stringify(candidate), candidate]));
	                const [electricalInterface] = distinct.size === 1 ? distinct.values() : [];
	                if (electricalInterface) {
	                    interfaces.set(cellName, electricalInterface);
	                    continue;
	                }
	                emitDecisionDiagnostic(diagnostics, 'placed-library-package-unresolved', `cell="${cellName}" library="${group.sourceLibName}" definitions=${distinct.size}`, 'CFB physical pin map', 'warning');
	            }
	            if (interfaces.size !== group.cellNames.size) {
	                continue;
	            }
	            const packageKinds = new Set([...interfaces.values()].map((electricalInterface) => JSON.stringify([electricalInterface.alphabeticNumbering, electricalInterface.isHomogeneous])));
	            if (packageKinds.size !== 1) {
	                emitPhysicalPinDiagnostic(diagnostics, group.packageName, 'placed-library-package-unresolved', `package="${group.packageName}" library="${group.sourceLibName}" interfaces=${packageKinds.size}`, group.sourceLibName, 'warning');
	                continue;
	            }
	            const pkg = buildPackageFromPlacedParts(group.packageName, group.sourceLibName, group.objects, undefined, physicalPadPresenceCatalog, diagnostics);
	            for (const libPart of pkg.LibPart) {
	                const electricalInterface = interfaces.get(stripNormalViewSuffix(libPart.CellName ?? pkg.Name));
	                const view = libPart.NormalView?.[0];
	                if (!electricalInterface || !view) {
	                    continue;
	                }
	                pkg.AlphabeticNumbering = electricalInterface.alphabeticNumbering;
	                pkg.IsHomogeneous = electricalInterface.isHomogeneous;
	                view.BoundingBox = { ...electricalInterface.boundingBox };
	                const livePinsByPosition = new Map([...(view.PinScalar ?? []), ...(view.PinBus ?? [])].map((pin) => [pin.Position, pin]));
	                const livePinPositions = new Set([
	                    ...(view.PinScalar ?? []).map((pin) => pin.Position),
	                    ...(view.PinBus ?? []).map((pin) => pin.Position),
	                ]);
	                const mappedPinPositions = new Set(electricalInterface.physicalParts.flatMap((part) => part.PinNumber?.map((pin) => pin.Position) ?? []));
	                const hasMismatchedLivePinSurface = mappedPinPositions.size !== livePinPositions.size ||
	                    ![...mappedPinPositions].every((position) => livePinPositions.has(position));
	                view.PinScalar = electricalInterface.scalarPins.map((pin) => ({
	                    ...pin,
	                    Name: hasMismatchedLivePinSurface ? pin.Name : (livePinsByPosition.get(pin.Position)?.Name ?? pin.Name),
	                }));
	                view.PinBus = electricalInterface.busPins.map((pin) => ({
	                    ...pin,
	                    Name: hasMismatchedLivePinSurface ? pin.Name : (livePinsByPosition.get(pin.Position)?.Name ?? pin.Name),
	                }));
	                libPart.PhysicalPart = electricalInterface.physicalParts.map((part) => ({
	                    PinNumber: part.PinNumber?.map((pin) => ({ ...pin })),
	                }));
	            }
	            resolved.set(key, pkg);
	        }
	        return [...resolved.values()];
	    }
	    function placedSourcePackageElectricalInterface(pkg, cellName) {
	        const parts = pkg.LibPart.filter((part) => stripNormalViewSuffix(part.CellName ?? pkg.Name) === cellName);
	        const interfaces = [];
	        for (const part of parts) {
	            const physicalParts = (part.PhysicalPart ?? []).map((physical) => ({
	                PinNumber: physical.PinNumber?.map((pin) => ({ Position: pin.Position, Number: pin.Number })),
	            }));
	            const physicalPositions = new Set(physicalParts.flatMap((physical) => physical.PinNumber?.map((pin) => pin.Position) ?? []));
	            for (const view of part.NormalView ?? []) {
	                const scalarPins = view.PinScalar ?? [];
	                const busPins = view.PinBus ?? [];
	                const logicalPositions = new Set([...scalarPins, ...busPins].map((pin) => pin.Position));
	                if (logicalPositions.size !== physicalPositions.size ||
	                    ![...logicalPositions].every((position) => physicalPositions.has(position))) {
	                    return undefined;
	                }
	                interfaces.push({
	                    boundingBox: view.BoundingBox,
	                    scalarPins,
	                    busPins,
	                    physicalParts,
	                });
	            }
	        }
	        const distinct = new Map(interfaces.map((candidate) => [JSON.stringify(candidate), candidate]));
	        if (distinct.size !== 1) {
	            return undefined;
	        }
	        const [electricalInterface] = distinct.values();
	        if (!electricalInterface) {
	            return undefined;
	        }
	        return {
	            alphabeticNumbering: pkg.AlphabeticNumbering,
	            isHomogeneous: pkg.IsHomogeneous,
	            boundingBox: electricalInterface.boundingBox,
	            scalarPins: electricalInterface.scalarPins,
	            busPins: electricalInterface.busPins,
	            physicalParts: electricalInterface.physicalParts,
	        };
	    }
	    function buildPackageFromPlacedParts(pkgName, sourceLibName, objects, catalogEntry, physicalPadPresenceCatalog, diagnostics) {
	        const referencePrefix = catalogEntry?.referencePrefix ?? unanimousPlacementReferencePrefix(pkgName, objects, diagnostics);
	        const libParts = buildLivePartLibParts(pkgName, objects, catalogEntry, physicalPadPresenceCatalog, referencePrefix);
	        const allPins = objects.flatMap((object) => object.materializedPortInstances ?? []);
	        return {
	            AlphabeticNumbering: 2,
	            IsHomogeneous: true,
	            Name: pkgName,
	            PcbFootprint: objects.find((object) => object.resolvedMetadata?.pcbLib)?.resolvedMetadata?.pcbLib ?? '',
	            PcbLib: '',
	            RefdesPrefix: referencePrefix,
	            SourceLibName: sourceLibName,
	            LibPart: libParts.length > 0 ?
	                libParts
	                : [
	                    {
	                        CellName: pkgName,
	                        NormalView: [
	                            buildLivePartNormalView(pkgName, allPins, catalogEntry, physicalPadPresenceCatalog, referencePrefix),
	                        ],
	                        PhysicalPart: [buildLivePhysicalPart(allPins)],
	                    },
	                ],
	        };
	    }
	    function buildLivePhysicalPart(pins) {
	        return {
	            PinNumber: pins.map((pin, index) => ({
	                Position: index,
	                Number: pin.physicalPinNumber ?? String(pin.pinIndex ?? pin.position ?? index + 1),
	            })),
	        };
	    }
	    function buildLivePartLibParts(pkgName, objects, catalogEntry, physicalPadPresenceCatalog, referencePrefix) {
	        const groups = new Map();
	        for (const object of objects) {
	            const cellName = stripNormalViewSuffix(object.graphicName);
	            if (!groups.has(cellName)) {
	                groups.set(cellName, object);
	            }
	        }
	        return Array.from(groups, ([cellName, object]) => {
	            const pins = object.materializedPortInstances ?? [];
	            return {
	                CellName: cellName,
	                NormalView: [
	                    buildLivePartNormalView(pkgName, pins, catalogEntry, physicalPadPresenceCatalog, referencePrefix, object),
	                ],
	                PhysicalPart: [buildLivePhysicalPart(pins)],
	            };
	        });
	    }
	    function unanimousPlacementReferencePrefix(pkgName, objects, diagnostics) {
	        const prefixes = new Set(objects
	            .map((object) => referencePrefixOfDesignator(object.reference ?? ''))
	            .filter((prefix) => prefix.length > 0));
	        if (prefixes.size === 1) {
	            return [...prefixes][0];
	        }
	        if (prefixes.size > 1) {
	            emitPhysicalPinDiagnostic(diagnostics, pkgName, 'package-reference-prefix-ambiguous', `package="${pkgName}" prefixes=${[...prefixes].sort().join(',')}`);
	        }
	        return '';
	    }
	    function buildLivePartNormalView(pkgName, pins, catalogEntry, physicalPadPresenceCatalog, referencePrefix, object) {
	        const keys = [
	            object?.graphicName ?? pkgName,
	            catalogEntry?.packageName ?? pkgName,
	            catalogEntry?.canonicalSourceLibName ?? '',
	        ];
	        const convertPin = (port) => materializedPortToSymbolPin(port, physicalPadPresenceAt(physicalPadPresenceCatalog, keys, pins.length, port.position ?? port.pinIndex ?? 0));
	        const scalarPins = pins.filter((pin) => !pin.isBus).map(convertPin);
	        const busPins = pins.filter((pin) => pin.isBus).map(convertPin);
	        return {
	            Suffix: NORMAL_VIEW_SUFFIX,
	            SymbolColor: color(),
	            BoundingBox: boundingBoxFromPins(pins),
	            ContentsLibName: '',
	            ContentsViewName: catalogEntry?.contentsViewName ?? '',
	            ContentsViewType: 5,
	            PartValue: catalogEntry?.partValue ?? object?.partValue ?? pkgName,
	            Reference: referencePrefix,
	            ...(scalarPins.length > 0 ? { PinScalar: scalarPins } : {}),
	            ...(busPins.length > 0 ? { PinBus: busPins } : {}),
	        };
	    }
	    function materializedPortToSymbolPin(port, physicalPadPresence) {
	        const implicitShield = isImplicitShieldPort(port, physicalPadPresence);
	        const shieldPin = isShieldPinName(port.name);
	        const type = implicitShield ? 7 : (port.type ?? DEFAULT_PIN_TYPE);
	        const powerPin = type === 7;
	        return {
	            Name: port.name,
	            Position: port.position ?? port.pinIndex ?? 0,
	            StartX: port.startX ?? port.x ?? 0,
	            StartY: port.startY ?? port.y ?? 0,
	            HotptX: port.hotptX ?? port.x ?? 0,
	            HotptY: port.hotptY ?? port.y ?? 0,
	            Type: type,
	            Visible: implicitShield || (shieldPin && type === 7 && port.visible !== true) || port.visible === false ? 0 : 1,
	            IsLong: powerPin,
	            IsClock: false,
	            IsDot: false,
	            IsLeftPointing: powerPin ? false : (port.hotptX ?? port.x ?? 0) < (port.startX ?? port.x ?? 0),
	            IsRightPointing: powerPin ? false : (port.hotptX ?? port.x ?? 0) > (port.startX ?? port.x ?? 0),
	            IsNetStyle: false,
	            IsGlobal: powerPin,
	            IsNoConnect: shieldPin ? false : port.isNoConnect,
	            IsNumberVisible: powerPin,
	        };
	    }
	    function boundingBoxFromPins(pins) {
	        const points = pins.flatMap((pin) => [
	            { x: pin.startX ?? pin.x ?? 0, y: pin.startY ?? pin.y ?? 0 },
	            { x: pin.hotptX ?? pin.x ?? 0, y: pin.hotptY ?? pin.y ?? 0 },
	        ]);
	        if (points.length === 0) {
	            return { X1: 0, Y1: 0, X2: 0, Y2: 0 };
	        }
	        return {
	            X1: Math.min(...points.map((point) => point.x)),
	            Y1: Math.min(...points.map((point) => point.y)),
	            X2: Math.max(...points.map((point) => point.x)),
	            Y2: Math.max(...points.map((point) => point.y)),
	        };
	    }
	    function boundingBoxFromSymbolPins(pins) {
	        return boundingBoxFromPins(pins.map((pin, position) => ({
	            name: pin.name,
	            position,
	            startX: pin.startX,
	            startY: pin.startY,
	            hotptX: pin.hotptX,
	            hotptY: pin.hotptY,
	            isNoConnect: false,
	            isBus: false,
	            source: 'symbol-pin',
	            isExplicit: false,
	        })));
	    }
	    function oneNormalViewPerSuffix(views, packageName, diagnostics) {
	        const viewsBySuffix = new Map();
	        for (const view of views) {
	            viewsBySuffix.set(view.Suffix, [...(viewsBySuffix.get(view.Suffix) ?? []), view]);
	        }
	        return [...viewsBySuffix.values()].map((sameSuffix) => {
	            const [first] = sameSuffix;
	            if (sameSuffix.length === 1) {
	                return first;
	            }
	            const kept = sameSuffix.find((view) => (view.PinScalar?.length ?? 0) + (view.PinBus?.length ?? 0) > 0) ?? first;
	            const graphicsDonor = hasNormalViewGraphics(kept) ? undefined : sameSuffix.find(hasNormalViewGraphics);
	            emitBudgetedDiagnostic(diagnostics, `${packageName}\0${kept.Suffix}\0same-suffix-views`, 'same-suffix-views-merged', `package "${packageName}" splits view "${kept.Suffix}" across ${sameSuffix.length} headers; kept the one with ${(kept.PinScalar?.length ?? 0) + (kept.PinBus?.length ?? 0)} pins`, 'CFB DSN package', 'info');
	            if (graphicsDonor === undefined) {
	                return kept;
	            }
	            const { PinScalar, PinBus, ...donorDrawing } = graphicsDonor;
	            return { ...donorDrawing, ...kept, BoundingBox: donorDrawing.BoundingBox };
	        });
	    }
	    function hasNormalViewGraphics(view) {
	        return !isEmptyNormalView({ ...view, PinScalar: undefined, PinBus: undefined });
	    }
	    function buildPackage(stream, physicalPinDiagnostics, placedViewEvidence) {
	        const groups = groupSymbolObjects(stream).filter(isXmlMaterializedPackageViewGroup);
	        const pinMaps = stream.decodedSymbolObjects?.filter(isPinNumberMap) ?? [];
	        const expectedArities = new Set(pinMaps.map((pinMap) => pinMap.entries.length));
	        const packageName = stream.parsed.packageName;
	        const groupViews = groups.map((group) => {
	            const cellName = stripNormalViewSuffix(group.name ?? packageName);
	            const directPins = group.records.filter(isSymbolPin);
	            const pins = directPins.length > 0 ?
	                directPins
	                : corroboratedSiblingViewPins(group, groups.map((candidate) => ({
	                    group: candidate,
	                    cellName: stripNormalViewSuffix(candidate.name ?? packageName),
	                    packageName,
	                    sourceLibName: '',
	                    pins: candidate.records.filter(isSymbolPin),
	                })), {
	                    cellName,
	                    packageName,
	                    sourceLibName: '',
	                    expectedArities,
	                    hasInformativePinMap: pinMaps.some((pinMap) => pinMap.entries.some((entry, position) => entry.number !== String(position + 1))),
	                    placedObjects: placedViewEvidence.get(cacheIdentityKey(cellName, packageName, '')) ?? [],
	                }, physicalPinDiagnostics);
	            return buildNormalView(group, pins);
	        });
	        const normalViews = oneNormalViewPerSuffix(groupViews, packageName, physicalPinDiagnostics);
	        const physicalParts = pinMaps.length > 0 ?
	            pinMaps.map((pinMap) => buildPhysicalPart(pinMap, normalViews[0]))
	            : [physicalPartFromNormalView(normalViews[0])];
	        const firstPartValue = stream.decodedSymbolObjects?.find(isPartValue);
	        if (firstPartValue?.referencePrefix === undefined) {
	            emitBudgetedDiagnostic(physicalPinDiagnostics, `${packageName}\0package-stream`, 'package-reference-prefix-unrecorded', `package "${packageName}" records no reference prefix, so it declares none`, 'CFB DSN package', 'info');
	        }
	        return {
	            AlphabeticNumbering: 2,
	            IsHomogeneous: true,
	            Name: stream.parsed.packageName,
	            PcbFootprint: firstPartValue?.value ?? '',
	            PcbLib: '',
	            RefdesPrefix: firstPartValue?.referencePrefix ?? '',
	            SourceLibName: '',
	            LibPart: [
	                {
	                    CellName: stream.parsed.packageName,
	                    ...(normalViews.length > 0 ? { NormalView: normalViews } : {}),
	                    PhysicalPart: physicalParts,
	                },
	            ],
	        };
	    }
	    function physicalPartFromNormalView(normalView) {
	        const pins = [...(normalView?.PinScalar ?? []), ...(normalView?.PinBus ?? [])];
	        return {
	            PinNumber: pins.map((pin) => ({ Position: pin.Position, Number: pin.Name })),
	        };
	    }
	    function buildNormalView(group, pinOverride) {
	        const geometry = primitiveGeometry(group.primitives);
	        const declaredBox = group.records.find((record) => record.kind === 'view-header')?.declaredBoundingBox;
	        const pins = pinOverride ?? group.records.filter(isSymbolPin);
	        const scalarPins = orderedSymbolPinEntries(pins.filter((pin) => !isBusName(pin.name)), pins).map(({ pin, position }) => symbolPinScalarFromCfb(pin, position));
	        const busPins = orderedSymbolPinEntries(pins.filter((pin) => isBusName(pin.name)), pins).map(({ pin, position }) => symbolPinScalarFromCfb(pin, position));
	        const displayProperties = group.records.filter(isSymbolDisplayProperty).map(symbolDisplayPropertyFromCfb);
	        const partValue = group.records.find(isPartValue);
	        return {
	            Suffix: decodedViewSuffix(group.name) ?? NORMAL_VIEW_SUFFIX,
	            ...(displayProperties.length > 0 ? { SymbolDisplayProperty: displayProperties } : {}),
	            SymbolColor: color(),
	            BoundingBox: declaredBox ?? boundingBoxFromGeometry(geometry, { excludeTextWhenPossible: true }),
	            ContentsLibName: '',
	            ContentsViewName: '',
	            ContentsViewType: 5,
	            PartValue: partValue?.value ?? '',
	            Reference: partValue?.referencePrefix ?? '',
	            ...(scalarPins.length > 0 ? { PinScalar: scalarPins } : {}),
	            ...(busPins.length > 0 ? { PinBus: busPins } : {}),
	            ...geometry,
	        };
	    }
	    function orderedSymbolPinEntries(pins, allPins) {
	        const entries = pins.map((pin) => ({ pin, position: allPins.indexOf(pin) }));
	        if (entries.length > 1) {
	            if (entries.every(({ pin }) => isNumericString(pin.name))) {
	                return entries.sort((left, right) => Number(left.pin.name) - Number(right.pin.name));
	            }
	        }
	        return entries;
	    }
	    function buildPhysicalPart(pinMap, normalView) {
	        const pinsByPosition = new Map([...(normalView?.PinScalar ?? []), ...(normalView?.PinBus ?? [])].map((pin) => [pin.Position, pin.Name]));
	        return {
	            PinNumber: pinMap.entries.map((entry) => ({
	                Position: entry.position,
	                Number: entry.number === '' ? (pinsByPosition.get(entry.position) ?? String(entry.position + 1)) : entry.number,
	            })),
	        };
	    }
	    function buildPortSymbol(stream) {
	        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
	        const pin = firstSymbolPin(stream);
	        return {
	            Name: stream.parsed.symbolName,
	            SourceLibName: '',
	            SymbolDisplayProperty: firstSymbolDisplayProperty(stream),
	            BoundingBox: portSymbolBoundingBox('', geometry),
	            ...(pin ? { PinScalar: symbolPinScalarFromCfb(pin) } : {}),
	            ...geometry,
	        };
	    }
	    function buildPortSymbolFromCacheGroup(group, sourceLibName = '', diagnostics) {
	        const geometry = primitiveGeometry(portSymbolPrimitives(group.name, group.primitives, diagnostics));
	        const pin = group.records.find(isSymbolPin);
	        const displayPropertyRecord = group.records.find(isSymbolDisplayProperty);
	        const displayProperty = displayPropertyRecord ? symbolDisplayPropertyFromCfb(displayPropertyRecord) : undefined;
	        return {
	            Name: group.name,
	            SourceLibName: sourceLibName,
	            ...(displayProperty ? { SymbolDisplayProperty: displayProperty } : {}),
	            BoundingBox: portSymbolBoundingBox(sourceLibName, geometry),
	            ...(pin ? { PinScalar: symbolPinScalarFromCfb(pin) } : {}),
	            ...geometry,
	        };
	    }
	    function portSymbolPrimitives(symbolName, primitives, diagnostics) {
	        const kept = primitives.filter((primitive) => primitiveMinimumCoordinate(primitive) >= 0);
	        if (kept.length !== primitives.length && diagnostics) {
	            emitPhysicalPinDiagnostic(diagnostics, symbolName, 'symbol-primitive-outside-frame', `symbol="${symbolName}" dropped=${primitives.length - kept.length}`);
	        }
	        return kept;
	    }
	    function primitiveMinimumCoordinate(primitive) {
	        if ('points' in primitive) {
	            return Math.min(...primitive.points.flatMap((point) => [point.x, point.y]));
	        }
	        if ('x1' in primitive) {
	            return Math.min(primitive.x1, primitive.y1, primitive.x2, primitive.y2);
	        }
	        return 0;
	    }
	    function portSymbolBoundingBox(sourceLibName, geometry) {
	        const decoded = boundingBoxFromGeometry(geometry);
	        if (decoded.X1 !== decoded.X2 || decoded.Y1 !== decoded.Y2) {
	            return decoded;
	        }
	        if (isCaptureBuiltInLibrary(sourceLibName)) {
	            return { X1: 0, X2: 30, Y1: 0, Y2: 20 };
	        }
	        return decoded;
	    }
	    const CAPTURE_SYMBOL_LIBRARY_NAME = 'CAPSYM';
	    function isCaptureBuiltInLibrary(sourceLibName) {
	        return (0, orcadLibraryProvenance_1.isFromCadenceLibrary)(sourceLibName, CAPTURE_SYMBOL_LIBRARY_NAME);
	    }
	    function offPageSymbolBoundingBox(symbolName, geometry) {
	        const boundingBox = boundingBoxFromGeometry(geometry);
	        if (/^OFFPAGEDUAL-[LR]$/i.test(symbolName) &&
	            boundingBox.X1 === 0 &&
	            boundingBox.X2 === 30 &&
	            boundingBox.Y1 === 0 &&
	            boundingBox.Y2 === 20) {
	            return { ...boundingBox, Y2: 21 };
	        }
	        return boundingBox;
	    }
	    function buildOffPageSymbol(stream) {
	        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
	        const pins = (stream.decodedSymbolObjects ?? []).filter(isSymbolPin);
	        return {
	            Name: stream.parsed.symbolName,
	            SourceLibName: '',
	            SymbolDisplayProperty: firstSymbolDisplayProperty(stream),
	            BoundingBox: offPageSymbolBoundingBox(stream.parsed.symbolName, geometry),
	            PinScalar: pins.map((pin, index) => offPageSymbolPinScalarFromCfb(pin, index)),
	            ...geometry,
	        };
	    }
	    function buildOffPageSymbolFromCacheGroup(group, sourceLibName = '') {
	        const geometry = primitiveGeometry(group.primitives);
	        const pins = group.records.filter(isSymbolPin);
	        const displayProperty = group.records.find(isSymbolDisplayProperty);
	        return {
	            Name: group.name,
	            SourceLibName: sourceLibName,
	            ...(displayProperty ? { SymbolDisplayProperty: symbolDisplayPropertyFromCfb(displayProperty) } : {}),
	            BoundingBox: offPageSymbolBoundingBox(group.name, geometry),
	            PinScalar: pins.map((pin, index) => offPageSymbolPinScalarFromCfb(pin, index)),
	            ...geometry,
	        };
	    }
	    function buildGlobalSymbol(stream, liveConnectorPins, diagnostics) {
	        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
	        const pins = (stream.decodedSymbolObjects ?? [])
	            .filter(isSymbolPin)
	            .map((pin, index) => globalSymbolPinScalarFromCfb(pin, index));
	        const corroboratedLivePin = liveConnectorSymbolPin(liveConnectorPins, stream.parsed.symbolName, '');
	        return {
	            Name: stream.parsed.symbolName,
	            SourceLibName: '',
	            BoundingBox: boundingBoxFromGeometry(geometry),
	            Line: geometry.Line ?? [],
	            PinScalar: pins.length > 0 ?
	                pins
	                : symbolPinsFromCorroboratedLivePin(stream.parsed.symbolName, corroboratedLivePin, diagnostics),
	            ...(firstSymbolDisplayProperty(stream) ? { SymbolDisplayProperty: firstSymbolDisplayProperty(stream) } : {}),
	            ...geometry,
	        };
	    }
	    function buildGlobalSymbolFromCacheGroup(group, sourceLibName, liveConnectorPins, diagnostics) {
	        const geometry = primitiveGeometry(group.primitives);
	        const pins = group.records.filter(isSymbolPin).map((pin, index) => globalSymbolPinScalarFromCfb(pin, index));
	        const displayProperty = group.records.find(isSymbolDisplayProperty);
	        const corroboratedLivePin = liveConnectorSymbolPin(liveConnectorPins, group.name, sourceLibName);
	        return {
	            Name: group.name,
	            SourceLibName: sourceLibName,
	            BoundingBox: boundingBoxFromGeometry(geometry),
	            Line: geometry.Line ?? [],
	            PinScalar: pins.length > 0 ? pins : symbolPinsFromCorroboratedLivePin(group.name, corroboratedLivePin, diagnostics),
	            ...(displayProperty ? { SymbolDisplayProperty: symbolDisplayPropertyFromCfb(displayProperty) } : {}),
	            ...geometry,
	        };
	    }
	    function symbolPinsFromCorroboratedLivePin(symbolName, pin, diagnostics) {
	        if (!pin) {
	            if (diagnostics) {
	                emitBudgetedDiagnostic(diagnostics, symbolName, 'global-pin-absent', `symbol="${symbolName}"`, 'CFB symbol', 'info');
	            }
	            return [];
	        }
	        return [{ ...globalSymbolPinScalarFromCfb(pin), Name: UNNAMED_SYMBOL_PIN }];
	    }
	    function buildErcSymbol(stream) {
	        const geometry = primitiveGeometry(stream.decodedPrimitives ?? []);
	        return {
	            Name: stream.parsed.symbolName,
	            SourceLibName: '',
	            BoundingBox: boundingBoxFromGeometry(geometry),
	            ...geometry,
	        };
	    }
	    function buildErcSymbolFromCacheGroup(group, sourceLibName = '') {
	        const geometry = primitiveGeometry(group.primitives);
	        return {
	            Name: group.name,
	            SourceLibName: sourceLibName,
	            BoundingBox: boundingBoxFromGeometry(geometry),
	            ...geometry,
	        };
	    }
	    function buildTitleBlockSymbol(document, pageSymbolRoles) {
	        const cacheStream = document.streams.find((stream) => stream.parsed?.kind === 'cache');
	        if (!cacheStream?.decodedPrimitives?.length) {
	            return undefined;
	        }
	        const titleBlockName = Array.from(pageSymbolRoles).find(([, role]) => role === 'titleblock')?.[0] ?? 'TitleBlock';
	        const geometry = primitiveGeometry(cacheStream.decodedPrimitives);
	        return {
	            Name: titleBlockName,
	            SourceLibName: '',
	            BoundingBox: boundingBoxFromGeometry(geometry),
	            ...geometry,
	        };
	    }
	    function buildPageNets(stream, options, aliasFont, diagnostics) {
	        const objects = stream.decodedPageObjects ?? [];
	        const wires = objects.filter(isWire);
	        const decodedAliases = objects.filter(isAlias);
	        const aliases = [
	            ...decodedAliases,
	            ...synthesizePageAliasesFromStrings(stream.lengthPrefixedStrings, wires, decodedAliases),
	        ];
	        const placedSymbols = objects.filter((object) => object.kind === 'placed-symbol');
	        const explicitJunctions = objects.filter(isJunction);
	        const busEntryCandidates = identifyBusEntryCandidates(wires, aliases);
	        const componentPinWireIds = findComponentPinAttachedWireIds(wires, placedSymbols);
	        const busEntryWireIds = new Set(busEntryCandidates.map((candidate) => candidate.wire.dbId));
	        const logicalBusWireIds = identifyLogicalBusWireIds(wires, aliases, busEntryCandidates);
	        const netWires = wires.filter((wire) => !busEntryWireIds.has(wire.dbId));
	        const metadataNetNames = buildMetadataNetNameLookup(stream.lengthPrefixedStrings, objects);
	        const netNameCarriers = buildWireNetNameCarriers(objects);
	        const pageBusMemberNames = new Set(aliases
	            .filter((alias) => isExplicitBusAliasName(alias.name))
	            .flatMap((alias) => collectBusMemberNames(stream, alias.name)));
	        const busMemberWireOwnership = buildBusMemberWireOwnership(wires, busEntryCandidates, aliases, metadataNetNames, logicalBusWireIds, componentPinWireIds, pageBusMemberNames, stream, diagnostics);
	        const busMemberWireIds = new Set(busMemberWireOwnership.ownerNameByWireId.keys());
	        const wireState = new Map();
	        for (const wire of netWires) {
	            const busMemberOwner = busMemberWireOwnership.ownerNameByWireId.get(wire.dbId);
	            const metadataName = metadataNetNames.get(wire.metadataId);
	            wireState.set(wire.dbId, {
	                wire,
	                aliases: [],
	                groupNames: busMemberOwner ? [busMemberOwner]
	                    : busMemberWireOwnership.conflictingWireIds.has(wire.dbId) ? []
	                        : metadataName ? [metadataName]
	                            : [],
	                junctions: (wire.junctions ?? []).map((junction) => ({ LocX: junction.locX, LocY: junction.locY })),
	            });
	        }
	        const precedingWireByOffset = buildPrecedingWireIndex(objects);
	        for (const alias of aliases) {
	            const recordedOwners = wires.filter((wire) => wire.dbId === alias.ownerWireDbId);
	            if (alias.ownerWireDbId !== undefined && recordedOwners.length !== 1) {
	                emitDecisionDiagnostic(diagnostics, 'alias-recorded-owner-unresolved', `alias=${JSON.stringify(alias.name)} offset=${alias.offset} wire=${alias.ownerWireDbId} candidates=${recordedOwners.length}: attachment withheld`, 'CFB alias', 'warning');
	                continue;
	            }
	            const owner = alias.ownerWireDbId === undefined ? findExactAliasOwner(alias, wires) : recordedOwners[0];
	            if (owner && busMemberWireOwnership.conflictingWireIds.has(owner.dbId)) {
	                continue;
	            }
	            if (owner && busEntryWireIds.has(owner.dbId)) {
	                continue;
	            }
	            const resolvedOwner = alias.ownerWireDbId === undefined ?
	                (owner ?? findNeighbouringAliasOwner(alias, netWires, precedingWireByOffset))
	                : owner;
	            if (resolvedOwner) {
	                wireState.get(resolvedOwner.dbId)?.aliases.push(alias);
	            }
	            else {
	                options.diagnostics?.push({
	                    severity: 'warning',
	                    message: `Could not attach CFB alias "${alias.name}" to a wire`,
	                    streamPath: stream.path,
	                    offset: alias.offset,
	                });
	                emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${alias.offset}`, 'alias-attachment-unbound', `alias="${alias.name}" at (${alias.locX},${alias.locY}) offset=${alias.offset}`, 'CFB alias', 'warning');
	            }
	        }
	        for (const junction of explicitJunctions) {
	            const owner = wireState.get(junction.wireDbId);
	            if (owner) {
	                owner.junctions.push({ LocX: junction.locX, LocY: junction.locY });
	            }
	        }
	        const wireJunctions = new Map(Array.from(wireState, ([wireDbId, state]) => [wireDbId, uniqueJunctions(state.junctions)]));
	        const groups = groupConnectedWires(netWires, wireState);
	        const netScalars = [];
	        const netBuses = [];
	        for (const group of groups) {
	            const aliasesInGroup = group.flatMap((wire) => wireState.get(wire.dbId)?.aliases ?? []);
	            const name = netNameForWireGroup(group, aliasesInGroup, netNameCarriers, metadataNetNames);
	            const hasScalarNetName = Boolean(name && !isBusName(name));
	            const busWires = hasScalarNetName ? [] : group.filter((wire) => logicalBusWireIds.has(wire.dbId) || isBusName(name));
	            const scalarWires = group.filter((wire) => {
	                if (busMemberWireIds.has(wire.dbId)) {
	                    return false;
	                }
	                return hasScalarNetName || (!logicalBusWireIds.has(wire.dbId) && !isBusName(name));
	            });
	            if (busWires.length > 0) {
	                const memberNetScalars = buildBusMemberNetScalars(busWires, busEntryCandidates, collectBusMemberNames(stream, name), wires, aliases, metadataNetNames, wireJunctions, aliasFont, busMemberWireOwnership);
	                netBuses.push({
	                    NetColor: DEFAULT_COLOR_VALUE,
	                    NetLineStyle: DEFAULT_NET_LINE_STYLE,
	                    NetLineWidth: DEFAULT_NET_LINE_WIDTH,
	                    Name: logicalNetBusName(name, memberNetScalars),
	                    WireBus: busWires.map((wire) => wireBusFromCfb(wire, wireState.get(wire.dbId)?.aliases ?? [], wireJunctions.get(wire.dbId) ?? [], aliasFont)),
	                    ...(memberNetScalars.length > 0 ? { NetScalar: memberNetScalars } : {}),
	                });
	            }
	            if (scalarWires.length > 0) {
	                netScalars.push({
	                    NetColor: DEFAULT_COLOR_VALUE,
	                    NetLineStyle: DEFAULT_NET_LINE_STYLE,
	                    NetLineWidth: DEFAULT_NET_LINE_WIDTH,
	                    Name: '',
	                    WireScalars: scalarWires.map((wire) => wireScalarFromCfb(wire, wireState.get(wire.dbId)?.aliases ?? [], uniqueJunctions(wireState.get(wire.dbId)?.junctions ?? []), aliasFont)),
	                });
	            }
	        }
	        return {
	            NetScalar: removeScalarWiresMaterializedAsBusMembers(netScalars, netBuses),
	            NetBus: netBuses.sort(compareNetBusXmlOrder),
	        };
	    }
	    function removeScalarWiresMaterializedAsBusMembers(netScalars, netBuses) {
	        const busMemberWireIds = new Set(netBuses.flatMap((bus) => (bus.NetScalar ?? []).flatMap((net) => (net.WireScalars ?? []).map((wire) => wire.DbId))));
	        if (busMemberWireIds.size === 0) {
	            return [...netScalars];
	        }
	        return netScalars
	            .map((net) => ({
	            ...net,
	            WireScalars: (net.WireScalars ?? []).filter((wire) => !busMemberWireIds.has(wire.DbId)),
	        }))
	            .filter((net) => (net.WireScalars ?? []).length > 0);
	    }
	    function buildBusMemberWireOwnership(wires, busEntryCandidates, aliases, metadataNetNames, logicalBusWireIds, componentPinWireIds, recognizedMemberNames, stream, diagnostics) {
	        const memberNames = new Set([
	            ...recognizedMemberNames,
	            ...busEntryCandidates.map((candidate) => candidate.alias.name),
	        ]);
	        const explicitClaimsByWireId = new Map();
	        const addExplicitClaim = (wireId, name) => {
	            if (!name || !memberNames.has(name)) {
	                return;
	            }
	            const claims = explicitClaimsByWireId.get(wireId) ?? new Set();
	            claims.add(name);
	            explicitClaimsByWireId.set(wireId, claims);
	        };
	        for (const candidate of busEntryCandidates) {
	            addExplicitClaim(candidate.wire.dbId, candidate.alias.name);
	        }
	        for (const alias of aliases) {
	            const owner = findExactAliasOwner(alias, wires);
	            if (owner) {
	                addExplicitClaim(owner.dbId, alias.name);
	            }
	        }
	        const ownerNameByWireId = new Map();
	        const conflictingWireIds = new Set();
	        for (const wire of wires) {
	            if (logicalBusWireIds.has(wire.dbId) && !componentPinWireIds.has(wire.dbId)) {
	                continue;
	            }
	            const explicitClaims = explicitClaimsByWireId.get(wire.dbId) ?? new Set();
	            if (explicitClaims.size > 1) {
	                const claims = [...explicitClaims].sort((left, right) => left.localeCompare(right));
	                emitBudgetedDiagnostic(diagnostics, `${stream.path}\0${wire.dbId}`, 'bus-member-multi-claim', `stream="${stream.path}" wireDbId=${wire.dbId} members="${claims.join(',')}"`, 'CFB bus member', 'error');
	                conflictingWireIds.add(wire.dbId);
	                continue;
	            }
	            const explicitOwner = [...explicitClaims][0];
	            const metadataOwner = metadataNetNames.get(wire.metadataId);
	            const owner = explicitOwner ?? (metadataOwner && memberNames.has(metadataOwner) ? metadataOwner : undefined);
	            if (owner) {
	                ownerNameByWireId.set(wire.dbId, owner);
	            }
	        }
	        return { ownerNameByWireId, conflictingWireIds };
	    }
	    function buildBusMemberNetScalars(busWires, busEntryCandidates, synthesizedMemberNames, wires, aliases, metadataNetNames, wireJunctions, aliasFont, ownership) {
	        const wiresByName = new Map();
	        const touchedCandidateNames = new Set();
	        const synthesizedMemberNameSet = new Set(synthesizedMemberNames);
	        const hasSynthesizedMemberRange = synthesizedMemberNameSet.size > 0;
	        const candidatesForBus = busEntryCandidates.filter((candidate) => busWires.some((wire) => pointOnWire(candidate.busEntry.BusX, candidate.busEntry.BusY, wire)));
	        for (const candidate of candidatesForBus) {
	            const name = candidate.alias.name;
	            if (name && (!hasSynthesizedMemberRange || synthesizedMemberNameSet.has(name))) {
	                touchedCandidateNames.add(name);
	            }
	        }
	        const memberNames = new Set([...synthesizedMemberNames, ...touchedCandidateNames]);
	        for (const name of memberNames) {
	            const memberWires = [];
	            for (const wire of wires) {
	                const owner = ownership.ownerNameByWireId.get(wire.dbId);
	                if (owner) {
	                    if (owner === name) {
	                        memberWires.push(wire);
	                    }
	                    continue;
	                }
	                if (ownership.conflictingWireIds.has(wire.dbId) || metadataNetNames.get(wire.metadataId) !== name) {
	                    continue;
	                }
	                ownership.ownerNameByWireId.set(wire.dbId, name);
	                memberWires.push(wire);
	            }
	            if (memberWires.length === 0) {
	                continue;
	            }
	            wiresByName.set(name, [
	                ...memberWires.map((wire) => {
	                    const memberAliases = aliases.filter((alias) => {
	                        if (alias.name !== name) {
	                            return false;
	                        }
	                        return findExactAliasOwner(alias, wires)?.dbId === wire.dbId;
	                    });
	                    const memberCandidates = candidatesForBus.filter((candidate) => candidate.alias.name === name && candidate.wire.dbId === wire.dbId);
	                    return wireScalarFromCfb(wire, memberAliases, uniqueJunctions([
	                        ...memberCandidates.flatMap((candidate) => busEntryJunctionsForWire(wire, candidate, wireJunctions)),
	                        ...busSpineEndpointJunctionsForWire(wire, busWires, wireJunctions),
	                        ...busMemberEndpointJunctionsForWire(wire, memberWires, wireJunctions),
	                    ]), aliasFont);
	                }),
	            ]);
	        }
	        for (const name of synthesizedMemberNames) {
	            if (!wiresByName.has(name)) {
	                wiresByName.set(name, []);
	            }
	        }
	        return Array.from(memberNames, (name) => {
	            const memberWires = wiresByName.get(name) ?? [];
	            const uniqueWires = uniqueWireScalarsByDbId(memberWires);
	            return {
	                NetColor: DEFAULT_COLOR_VALUE,
	                NetLineStyle: DEFAULT_NET_LINE_STYLE,
	                NetLineWidth: DEFAULT_NET_LINE_WIDTH,
	                Name: name,
	                WireScalars: uniqueWires.length > 0 ? uniqueWires : undefined,
	            };
	        }).sort((left, right) => left.Name.localeCompare(right.Name));
	    }
	    function busEntryJunctionsForWire(wire, candidate, wireJunctions) {
	        const junctions = [...(wireJunctions.get(wire.dbId) ?? [])];
	        if (pointOnWire(candidate.busEntry.BusX, candidate.busEntry.BusY, wire)) {
	            junctions.push({ LocX: candidate.busEntry.BusX, LocY: candidate.busEntry.BusY });
	        }
	        return uniqueJunctions(junctions);
	    }
	    function busSpineEndpointJunctionsForWire(wire, busWires, wireJunctions) {
	        const junctions = [...(wireJunctions.get(wire.dbId) ?? [])];
	        for (const endpoint of [
	            { LocX: wire.startX, LocY: wire.startY },
	            { LocX: wire.endX, LocY: wire.endY },
	        ]) {
	            if (busWires.some((busWire) => pointOnWire(endpoint.LocX, endpoint.LocY, busWire))) {
	                junctions.push(endpoint);
	            }
	        }
	        return uniqueJunctions(junctions);
	    }
	    function busMemberEndpointJunctionsForWire(wire, memberWires, wireJunctions) {
	        const junctions = [...(wireJunctions.get(wire.dbId) ?? [])];
	        for (const endpoint of [
	            { LocX: wire.startX, LocY: wire.startY },
	            { LocX: wire.endX, LocY: wire.endY },
	        ]) {
	            if (isBusMemberJunctionOwner(wire, memberWires, endpoint)) {
	                junctions.push(endpoint);
	            }
	        }
	        return uniqueJunctions(junctions);
	    }
	    function isBusMemberJunctionOwner(wire, memberWires, endpoint) {
	        const touchingWires = memberWires.filter((memberWire) => pointOnWire(endpoint.LocX, endpoint.LocY, memberWire));
	        if (touchingWires.length < 3) {
	            return false;
	        }
	        const owner = touchingWires
	            .filter((memberWire) => isWireEndpoint(memberWire, endpoint.LocX, endpoint.LocY))
	            .sort((left, right) => Math.max(right.startX, right.endX) - Math.max(left.startX, left.endX))[0];
	        return owner?.dbId === wire.dbId;
	    }
	    function uniqueWireScalarsByDbId(wires) {
	        const byDbId = new Map();
	        const unique = [];
	        for (const wire of wires) {
	            const existing = byDbId.get(wire.DbId);
	            if (existing) {
	                const aliases = [...(existing.Alias ?? []), ...(wire.Alias ?? [])];
	                if (aliases.length > 0) {
	                    existing.Alias = uniqueAliases(aliases);
	                }
	                const junctions = [...(existing.Junction ?? []), ...(wire.Junction ?? [])];
	                if (junctions.length > 0) {
	                    existing.Junction = uniqueJunctions(junctions);
	                }
	                continue;
	            }
	            byDbId.set(wire.DbId, wire);
	            unique.push(wire);
	        }
	        return unique;
	    }
	    function uniqueAliases(aliases) {
	        const seen = new Set();
	        const unique = [];
	        for (const alias of aliases) {
	            const key = `${alias.Name}:${alias.LocX}:${alias.LocY}`;
	            if (seen.has(key)) {
	                continue;
	            }
	            seen.add(key);
	            unique.push(alias);
	        }
	        return unique;
	    }
	    function synthesizePageAliasesFromStrings(strings, wires, existingAliases) {
	        const existing = new Set(existingAliases.map((alias) => `${alias.name}:${alias.locX}:${alias.locY}`));
	        const synthesized = [];
	        for (const string of strings) {
	            const locX = string.precedingInt32Le20;
	            const locY = string.precedingInt32Le16;
	            if (locX === undefined ||
	                locY === undefined ||
	                !isCoordinateBackedAliasString(string, locX, locY) ||
	                !findExactPointOwner(locX, locY, wires)) {
	                continue;
	            }
	            const name = string.value.trim();
	            const key = `${name}:${locX}:${locY}`;
	            if (existing.has(key)) {
	                continue;
	            }
	            existing.add(key);
	            const byteLength = 2 + string.length + (string.hasNullTerminator ? 1 : 0);
	            synthesized.push({
	                kind: 'alias',
	                offset: string.offset,
	                byteLength,
	                payloadByteLength: byteLength,
	                name,
	                locX,
	                locY,
	                color: DEFAULT_COLOR_VALUE,
	                rotation: 0,
	            });
	        }
	        return synthesized;
	    }
	    function isCoordinateBackedAliasString(string, locX, locY) {
	        return (Number.isFinite(locX) &&
	            Number.isFinite(locY) &&
	            locX >= 0 &&
	            locY >= 0 &&
	            string.followingUInt32Le === PAGE_ALIAS_STRING_MARKER &&
	            isPlausibleCoordinateAliasName(string.value));
	    }
	    function isPlausibleCoordinateAliasName(value) {
	        const name = value.trim();
	        return name.length > 0 && name.length <= 128 && /^[A-Za-z0-9_+.\-#:[\]/]+$/.test(name) && !/^\d+$/.test(name);
	    }
	    function collectBusMemberNames(stream, busName) {
	        const details = busNameDetails(busName);
	        if (!details) {
	            return [];
	        }
	        const values = [
	            ...(stream.lengthPrefixedStrings ?? []).map((entry) => entry.value),
	            ...(stream.asciiRuns ?? []).map((entry) => entry.value),
	        ];
	        const names = new Set();
	        for (const value of values) {
	            if (isBusMemberNameForRange(value, details)) {
	                names.add(value);
	            }
	        }
	        return Array.from(names).sort((left, right) => left.localeCompare(right));
	    }
	    function busNameDetails(busName) {
	        const match = /^(.+)\[(\d+)(?:\.\.|:)(\d+)\]$/.exec(busName);
	        if (!match) {
	            return undefined;
	        }
	        return { prefix: match[1], start: Number(match[2]), end: Number(match[3]) };
	    }
	    function isBusMemberNameForRange(value, details) {
	        if (value.includes('[')) {
	            return false;
	        }
	        if (value.startsWith(`${details.prefix}.`)) {
	            return true;
	        }
	        const memberIndex = busMemberIndexForPrefix(value, details.prefix);
	        if (memberIndex === undefined) {
	            return false;
	        }
	        return memberIndex >= Math.min(details.start, details.end) && memberIndex <= Math.max(details.start, details.end);
	    }
	    function busMemberIndexForPrefix(value, prefix) {
	        const dottedPrefix = `${prefix}.`;
	        if (value.startsWith(dottedPrefix)) {
	            const match = /^(\d+)/.exec(value.slice(dottedPrefix.length));
	            return match ? Number(match[1]) : undefined;
	        }
	        if ((!/^[A-Za-z_]+$/.test(prefix) && !prefix.endsWith('_')) || !value.startsWith(prefix)) {
	            return undefined;
	        }
	        const match = /^(\d+)/.exec(value.slice(prefix.length));
	        return match ? Number(match[1]) : undefined;
	    }
	    function logicalNetBusName(name, memberNetScalars) {
	        const details = busNameDetails(name);
	        if (!details || memberNetScalars.length === 0) {
	            return name;
	        }
	        if (memberNetScalars.every((member) => member.Name.startsWith(details.prefix) && !member.Name.startsWith(`${details.prefix}.`))) {
	            return '';
	        }
	        return name;
	    }
	    function compareNetBusXmlOrder(left, right) {
	        const leftDetails = netBusSortDetails(left.Name);
	        const rightDetails = netBusSortDetails(right.Name);
	        if (leftDetails.base === rightDetails.base && leftDetails.memorySide !== rightDetails.memorySide) {
	            return leftDetails.memorySide ? 1 : -1;
	        }
	        return 0;
	    }
	    function netBusSortDetails(name) {
	        const details = busNameDetails(name);
	        const prefix = details?.prefix ?? name;
	        return {
	            base: prefix.replace(/_M$/i, ''),
	            memorySide: /_M$/i.test(prefix),
	        };
	    }
	    function buildPageObjectNetNameLookup(objects, strings = [], connectorSymbolGeometries, diagnostics) {
	        const wires = objects.filter(isWire);
	        const aliases = objects.filter(isAlias);
	        const busEntryWireIds = identifyBusEntryWireIds(wires, aliases);
	        const netWires = wires.filter((wire) => !busEntryWireIds.has(wire.dbId));
	        const metadataNetNames = buildMetadataNetNameLookup(strings, objects, diagnostics);
	        const netNameCarriers = buildWireNetNameCarriers(objects);
	        const placedRecordNetNames = buildPlacedRecordNetNameLookup(objects, metadataNetNames);
	        const wireState = new Map();
	        const exactAliasesByWireId = new Map();
	        const names = new Map();
	        for (const wire of netWires) {
	            wireState.set(wire.dbId, {
	                wire,
	                aliases: [],
	                groupNames: metadataNetNames.get(wire.metadataId) ? [metadataNetNames.get(wire.metadataId)] : [],
	                junctions: [],
	            });
	        }
	        const precedingWireByOffset = buildPrecedingWireIndex(objects);
	        for (const alias of aliases) {
	            const owner = findExactAliasOwner(alias, wires);
	            if (owner && busEntryWireIds.has(owner.dbId)) {
	                continue;
	            }
	            if (owner) {
	                const exactAliases = exactAliasesByWireId.get(owner.dbId) ?? [];
	                exactAliases.push(alias);
	                exactAliasesByWireId.set(owner.dbId, exactAliases);
	            }
	            const resolvedOwner = owner ?? findNeighbouringAliasOwner(alias, netWires, precedingWireByOffset);
	            if (resolvedOwner) {
	                wireState.get(resolvedOwner.dbId)?.aliases.push(alias);
	            }
	        }
	        const groups = groupConnectedWires(netWires, wireState);
	        const groupNameByWireId = new Map();
	        for (const group of groups) {
	            const aliasesInGroup = group.flatMap((wire) => wireState.get(wire.dbId)?.aliases ?? []);
	            const name = netNameForWireGroup(group, aliasesInGroup, netNameCarriers, metadataNetNames);
	            if (!name) {
	                continue;
	            }
	            for (const wire of group) {
	                groupNameByWireId.set(wire.dbId, name);
	            }
	        }
	        const netIdentityByWireId = wireNetIdentities(groups, new Map([...wireState].map(([wireDbId, state]) => [wireDbId, state.aliases])), netNameCarriers, metadataNetNames);
	        for (const object of objects) {
	            if (object.kind !== 'placed-symbol' || object.role === 'part-inst' || object.role === 'titleblock') {
	                continue;
	            }
	            const adjacentDrawnPortName = object.role === 'global' ? findAdjacentDrawnPortNetName(object, objects) : undefined;
	            if (adjacentDrawnPortName) {
	                names.set(object.dbId, adjacentDrawnPortName);
	                continue;
	            }
	            const explicitObjectName = metadataNetNames.get(object.dbId);
	            const decodedPlacedName = (object.name &&
	                !isGenericPlacedSymbolCarrierName(object, object.name) &&
	                !isNumericConnectorRecordName(object, object.name) &&
	                plausibleNetNameCandidate(object, object.name, diagnostics)) ?
	                object.name
	                : undefined;
	            const directObjectName = explicitObjectName && !isGenericPlacedSymbolCarrierName(object, explicitObjectName) ?
	                explicitObjectName
	                : undefined;
	            const groupedPlacedRecordName = placedRecordNetNames.get(placedRecordNameKey(object));
	            explicitObjectName && isGenericPlacedSymbolCarrierName(object, explicitObjectName) ? explicitObjectName : (undefined);
	            const owner = findPlacedSymbolWireOwner(object, wires, connectorSymbolGeometries, diagnostics, netIdentityByWireId);
	            const ownerAliasName = owner ? firstNetName(exactAliasesByWireId.get(owner.dbId) ?? []) : '';
	            const connectedGroupName = owner ? groupNameByWireId.get(owner.dbId) : undefined;
	            const exactAttachedWireName = owner ?
	                (connectedGroupName ??
	                    metadataNetNames.get(owner.metadataId) ??
	                    (ownerAliasName ? ownerAliasName : undefined))
	                : undefined;
	            const name = placedSymbolNetName(object, exactAttachedWireName, connectedGroupName, directObjectName, decodedPlacedName, groupedPlacedRecordName, diagnostics);
	            if (name) {
	                names.set(object.dbId, name);
	                continue;
	            }
	        }
	        return names;
	    }
	    function buildPlacedRecordNetNameLookup(objects, metadataNetNames) {
	        const candidates = new Map();
	        for (const object of objects) {
	            if (object.kind !== 'placed-symbol' || object.role !== 'global') {
	                continue;
	            }
	            const name = metadataNetNames.get(object.dbId);
	            if (!name || isGenericPlacedSymbolCarrierName(object, name)) {
	                continue;
	            }
	            const key = placedRecordNameKey(object);
	            const names = candidates.get(key) ?? new Set();
	            names.add(name);
	            candidates.set(key, names);
	        }
	        return new Map([...candidates].flatMap(([key, names]) => (names.size === 1 ? [[key, names.values().next().value]] : [])));
	    }
	    function placedRecordNameKey(object) {
	        return `${object.symbolName}\0${object.recordType}`;
	    }
	    function placedSymbolNetName(object, exactAttachedWireName, connectedGroupName, directObjectName, decodedPlacedName, groupedPlacedRecordName, diagnostics) {
	        if (directObjectName && plausibleNetNameCandidate(object, directObjectName, diagnostics)) {
	            return directObjectName;
	        }
	        if (decodedPlacedName && plausibleNetNameCandidate(object, decodedPlacedName, diagnostics)) {
	            return decodedPlacedName;
	        }
	        if (exactAttachedWireName && plausibleNetNameCandidate(object, exactAttachedWireName, diagnostics)) {
	            return exactAttachedWireName;
	        }
	        if (groupedPlacedRecordName && plausibleNetNameCandidate(object, groupedPlacedRecordName, diagnostics)) {
	            return groupedPlacedRecordName;
	        }
	        return connectedGroupName && plausibleNetNameCandidate(object, connectedGroupName, diagnostics) ? connectedGroupName
	            : undefined;
	    }
	    function isNumericConnectorRecordName(object, name) {
	        return object.role !== 'global' && /^\d+$/.test(name.trim());
	    }
	    function plausibleNetNameCandidate(object, name, diagnostics) {
	        if ((0, portOccurrenceNormalization_1.isPlausibleNetName)(name)) {
	            return true;
	        }
	        if (diagnostics) {
	            emitBudgetedDiagnostic(diagnostics, `${object.dbId}\0${name}`, 'implausible-net-name', `dbId=${object.dbId} symbol="${object.symbolName}" name="${name}"`, 'CFB connector', 'warning');
	        }
	        return false;
	    }
	    function firstPlausiblePlacedSymbolNetName(object, candidates, diagnostics) {
	        for (const candidate of candidates) {
	            if (candidate && plausibleNetNameCandidate(object, candidate, diagnostics)) {
	                return candidate;
	            }
	        }
	        if (diagnostics) {
	            emitBudgetedDiagnostic(diagnostics, `${object.dbId}`, 'connector-name-undecoded', `dbId=${object.dbId} symbol="${object.symbolName}" role=${object.role}`, 'CFB connector', 'warning');
	        }
	        return '';
	    }
	    function fillMissingPlacedConnectorNetNames(objects, names, strings, connectorSymbolGeometries, diagnostics) {
	        const wires = objects.filter(isWire);
	        const metadataNetNames = buildMetadataNetNameLookup(strings, objects, diagnostics);
	        const precedingWireByOffset = buildPrecedingWireIndex(objects);
	        const aliasesByWireId = new Map();
	        for (const alias of objects.filter(isAlias)) {
	            const owner = findExactAliasOwner(alias, wires) ?? findNeighbouringAliasOwner(alias, wires, precedingWireByOffset);
	            if (owner) {
	                aliasesByWireId.set(owner.dbId, [...(aliasesByWireId.get(owner.dbId) ?? []), alias]);
	            }
	        }
	        const netIdentityByWireId = wireNetIdentities(groupConnectedWires(wires, new Map()), aliasesByWireId, buildWireNetNameCarriers(objects), metadataNetNames);
	        for (const object of objects) {
	            if (object.kind !== 'placed-symbol' ||
	                object.role === 'part-inst' ||
	                object.role === 'titleblock' ||
	                names.has(object.dbId)) {
	                continue;
	            }
	            const recovered = closestResolvedSameSymbolName(object, objects, names, wires, metadataNetNames, connectorSymbolGeometries, diagnostics, netIdentityByWireId);
	            if (recovered && (0, portOccurrenceNormalization_1.isPlausibleNetName)(recovered) && recovered !== object.symbolName) {
	                names.set(object.dbId, recovered);
	                if (diagnostics) {
	                    emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-proximity-recovery', `dbId=${object.dbId} symbol="${object.symbolName}" recovered="${recovered}"`, 'CFB connector', 'warning');
	                }
	            }
	        }
	    }
	    function closestResolvedSameSymbolName(object, objects, names, wires, metadataNetNames, connectorSymbolGeometries, diagnostics, netIdentityByWireId) {
	        const maximumPeerDistance = 600;
	        let bestSameRecord;
	        const sameRecordNames = new Set();
	        let best;
	        const peerNames = new Set();
	        for (const candidate of objects) {
	            if (candidate.kind !== 'placed-symbol' ||
	                candidate.dbId === object.dbId ||
	                candidate.symbolName !== object.symbolName) {
	                continue;
	            }
	            const name = names.get(candidate.dbId);
	            if (!name || !(0, portOccurrenceNormalization_1.isPlausibleNetName)(name) || name === candidate.symbolName) {
	                continue;
	            }
	            const distance = Math.abs(candidate.locX - object.locX) + Math.abs(candidate.locY - object.locY);
	            if (candidate.recordType === object.recordType) {
	                sameRecordNames.add(name);
	                if (!bestSameRecord ||
	                    distance < bestSameRecord.distance ||
	                    (distance === bestSameRecord.distance && name < bestSameRecord.name)) {
	                    bestSameRecord = { distance, name };
	                }
	            }
	            if (distance > maximumPeerDistance) {
	                continue;
	            }
	            peerNames.add(name);
	            if (!best || distance < best.distance || (distance === best.distance && name < best.name)) {
	                best = { distance, name };
	            }
	        }
	        if (bestSameRecord && sameRecordNames.size === 1) {
	            if (bestSameRecord.distance > maximumPeerDistance && diagnostics) {
	                emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-same-record-peer-distant', `dbId=${object.dbId} symbol="${object.symbolName}" distance=${bestSameRecord.distance}`, 'CFB connector', 'warning');
	            }
	            return bestSameRecord.name;
	        }
	        if (!best) {
	            return undefined;
	        }
	        const attachedName = findPlacedSymbolWireOwner(object, wires, connectorSymbolGeometries, diagnostics, netIdentityByWireId);
	        const isCorroborated = attachedName && metadataNetNames.get(attachedName.metadataId) === best.name;
	        if (peerNames.size === 1 || isCorroborated) {
	            return best.name;
	        }
	        if (diagnostics) {
	            emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-name-unresolved', `dbId=${object.dbId} symbol="${object.symbolName}" peerNames="${[...peerNames].sort().join(',')}"`, 'CFB connector', 'warning');
	        }
	        return undefined;
	    }
	    function isGenericPlacedSymbolCarrierName(object, name) {
	        return name.trim().toUpperCase() === object.symbolName.trim().toUpperCase();
	    }
	    let NetNameCarrierRank;
	    (function (NetNameCarrierRank) {
	        NetNameCarrierRank[NetNameCarrierRank["Unattached"] = 0] = "Unattached";
	        NetNameCarrierRank[NetNameCarrierRank["AliasLabel"] = 1] = "AliasLabel";
	        NetNameCarrierRank[NetNameCarrierRank["GlobalSymbol"] = 2] = "GlobalSymbol";
	        NetNameCarrierRank[NetNameCarrierRank["BoundaryConnector"] = 3] = "BoundaryConnector";
	    })(NetNameCarrierRank || (NetNameCarrierRank = {}));
	    function placedSymbolCarrierRank(role) {
	        if (role === 'off-page-connector' || role === 'port') {
	            return NetNameCarrierRank.BoundaryConnector;
	        }
	        return role === 'global' ? NetNameCarrierRank.GlobalSymbol : undefined;
	    }
	    function buildWireNetNameCarriers(objects) {
	        const wires = objects.filter(isWire);
	        const carriers = new Map();
	        const record = (metadataId, name, rank) => {
	            const key = name.trim().toUpperCase();
	            if (!key) {
	                return;
	            }
	            const byName = carriers.get(metadataId) ?? new Map();
	            byName.set(key, Math.max(byName.get(key) ?? NetNameCarrierRank.Unattached, rank));
	            carriers.set(metadataId, byName);
	        };
	        const precedingWireByOffset = buildPrecedingWireIndex(objects);
	        for (const alias of objects.filter(isAlias)) {
	            const owner = findExactAliasOwner(alias, wires) ?? findNeighbouringAliasOwner(alias, wires, precedingWireByOffset);
	            if (owner) {
	                record(owner.metadataId, alias.name, NetNameCarrierRank.AliasLabel);
	            }
	        }
	        for (const object of objects) {
	            if (object.kind !== 'placed-symbol') {
	                continue;
	            }
	            const rank = placedSymbolCarrierRank(object.role);
	            if (rank === undefined) {
	                continue;
	            }
	            const carried = [object.name?.trim(), object.role === 'global' ? object.symbolName.trim() : undefined].filter((value) => Boolean(value));
	            if (carried.length === 0) {
	                continue;
	            }
	            const owner = findPlacedSymbolWireOwner(object, wires);
	            for (const value of carried) {
	                record(object.dbId, value, rank);
	                if (owner) {
	                    record(owner.metadataId, value, rank);
	                }
	            }
	        }
	        return carriers;
	    }
	    function netNameForWireGroup(group, aliasesInGroup, carriers, metadataNetNames) {
	        const rankOf = (value) => {
	            const key = value.trim().toUpperCase();
	            let best = NetNameCarrierRank.Unattached;
	            for (const wire of group) {
	                const rank = carriers.get(wire.metadataId)?.get(key);
	                if (rank !== undefined && rank > best) {
	                    best = rank;
	                }
	            }
	            return best;
	        };
	        const candidates = [];
	        for (const wire of group) {
	            const name = metadataNetNames.get(wire.metadataId);
	            if (name && !candidates.includes(name)) {
	                candidates.push(name);
	            }
	        }
	        for (const alias of aliasesInGroup) {
	            if (alias.name && !candidates.includes(alias.name)) {
	                candidates.push(alias.name);
	            }
	        }
	        let best = '';
	        let bestRank = NetNameCarrierRank.Unattached;
	        for (const candidate of candidates) {
	            const rank = rankOf(candidate);
	            if (best === '' || rank > bestRank) {
	                best = candidate;
	                bestRank = rank;
	            }
	        }
	        return best;
	    }
	    function buildMetadataNetNameLookup(strings, objects, diagnostics) {
	        const carriers = buildWireNetNameCarriers(objects);
	        const rankOf = (metadataId, value) => carriers.get(metadataId)?.get(value.trim().toUpperCase()) ?? NetNameCarrierRank.Unattached;
	        const chosen = new Map();
	        for (const entry of strings) {
	            const sheetOwner = objects.find((object) => object.kind === 'drawn-inst' &&
	                object.recordFrame !== undefined &&
	                entry.offset >= object.recordFrame.offset &&
	                entry.offset + 2 + entry.length + 1 <= object.recordFrame.endOffset);
	            if (sheetOwner !== undefined) {
	                if (diagnostics !== undefined) {
	                    emitDecisionDiagnostic(diagnostics, 'net-name-owned-sheet-frame', `offset=${entry.offset}: counted string belongs to a sheet frame, not a page net record`, 'CFB net name', 'info');
	                }
	                continue;
	            }
	            if (entry.followingUInt32Le === undefined) {
	                continue;
	            }
	            if (!(0, portOccurrenceNormalization_1.isPlausibleNetName)(entry.value)) {
	                if (diagnostics) {
	                    emitBudgetedDiagnostic(diagnostics, `${entry.followingUInt32Le}\0${entry.value}`, 'implausible-net-name', `metadataId=${entry.followingUInt32Le} name="${entry.value}"`, 'CFB net name', 'warning');
	                }
	                continue;
	            }
	            const metadataId = entry.followingUInt32Le;
	            const rank = rankOf(metadataId, entry.value);
	            const existing = chosen.get(metadataId);
	            if (existing === undefined || rank > existing.rank) {
	                chosen.set(metadataId, { name: entry.value, rank, tied: [entry.value] });
	                continue;
	            }
	            if (rank === existing.rank && entry.value !== existing.name && !existing.tied.includes(entry.value)) {
	                existing.tied.push(entry.value);
	            }
	        }
	        const names = new Map();
	        for (const [metadataId, choice] of chosen) {
	            names.set(metadataId, choice.name);
	            if (diagnostics && choice.tied.length > 1) {
	                emitBudgetedDiagnostic(diagnostics, String(metadataId), 'net-name-ambiguous', `metadataId=${metadataId} rank=${NetNameCarrierRank[choice.rank]} candidates=${choice.tied
	                    .map((value) => `"${value}"`)
	                    .join(', ')} read="${choice.name}"`, 'CFB net name', 'warning');
	            }
	        }
	        return names;
	    }
	    function findPlacedSymbolWireOwner(object, wires, connectorSymbolGeometries, diagnostics, netIdentityByWireId) {
	        const { points, isDecoded } = placedSymbolConnectionPoints(object, connectorSymbolGeometries, diagnostics);
	        const exactOwners = points.flatMap((point) => findExactPointOwners(point.x, point.y, wires));
	        if (exactOwners.length > 0) {
	            return singleNetWireOwner(exactOwners, object, 'connector-probe-nets-competing', netIdentityByWireId, diagnostics);
	        }
	        let bestDistance = Number.POSITIVE_INFINITY;
	        let nearest = [];
	        for (const point of points) {
	            for (const wire of wires) {
	                const distance = distanceToWire(point.x, point.y, wire);
	                if (distance > 25 || distance > bestDistance) {
	                    continue;
	                }
	                if (distance < bestDistance) {
	                    bestDistance = distance;
	                    nearest = [];
	                }
	                nearest.push(wire);
	            }
	        }
	        if (nearest.length === 0) {
	            return undefined;
	        }
	        return isDecoded ?
	            nearest[0]
	            : singleNetWireOwner(nearest, object, 'connector-probe-nets-equidistant', netIdentityByWireId, diagnostics);
	    }
	    function singleNetWireOwner(wires, object, code, netIdentityByWireId, diagnostics) {
	        const identities = wires.map((wire) => netIdentityByWireId?.get(wire.dbId) ?? { run: `record:${wire.dbId}` });
	        const keys = new Set(identities.map((identity) => identity.name ?? identity.run));
	        if (keys.size <= 1) {
	            return wires[0];
	        }
	        const named = identities.some((identity) => identity.name !== undefined);
	        if (diagnostics) {
	            emitBudgetedDiagnostic(diagnostics, String(object.dbId), named ? code : 'connector-probe-runs-unnamed', `dbId=${object.dbId} symbol="${object.symbolName}" nets=${keys.size}`, 'CFB connector', 'warning');
	        }
	        return undefined;
	    }
	    function placedSymbolConnectionPoints(object, catalog, diagnostics) {
	        if (isPlacedPortSymbol(object)) {
	            const preferRightHotPoint = (object.displayProperties ?? []).some((property) => property.locX < 0);
	            if (preferRightHotPoint) {
	                return {
	                    isDecoded: true,
	                    points: [
	                        { x: object.locX, y: object.locY + 10 },
	                        { x: object.locX + 30, y: object.locY + 10 },
	                        { x: object.locX + 10, y: object.locY + 30 },
	                        { x: object.locX, y: object.locY },
	                        { x: object.locX + 10, y: object.locY + 10 },
	                        { x: object.locX + 10, y: object.locY + 20 },
	                    ],
	                };
	            }
	            return {
	                isDecoded: true,
	                points: [
	                    { x: object.locX, y: object.locY + 10 },
	                    { x: object.locX + 10, y: object.locY + 30 },
	                    { x: object.locX + 30, y: object.locY + 10 },
	                    { x: object.locX, y: object.locY },
	                    { x: object.locX + 10, y: object.locY + 10 },
	                    { x: object.locX + 10, y: object.locY + 20 },
	                ],
	            };
	        }
	        const geometry = decodedConnectorGeometryForPlacedSymbol(object, catalog);
	        if (geometry) {
	            return { isDecoded: true, points: transformedConnectorGeometryPoints(object, [geometry]) };
	        }
	        const decodedCandidates = (catalog?.candidatesBySymbol.get(liveConnectorPinSymbolKey(object.symbolName)) ?? []).filter((candidate) => candidate.hasDecodedBounds === true);
	        if (decodedCandidates.length > 0) {
	            return { isDecoded: true, points: transformedConnectorGeometryPoints(object, decodedCandidates) };
	        }
	        if (diagnostics) {
	            emitBudgetedDiagnostic(diagnostics, String(object.dbId), 'connector-generic-probe', `dbId=${object.dbId} symbol="${object.symbolName}" loc=(${object.locX},${object.locY})`, 'CFB connector', 'warning');
	        }
	        return {
	            isDecoded: false,
	            points: [
	                { x: object.locX, y: object.locY },
	                { x: object.locX, y: object.locY + 10 },
	                { x: object.locX + 10, y: object.locY },
	                { x: object.locX + 10, y: object.locY + 10 },
	                { x: object.locX + 10, y: object.locY + 20 },
	            ],
	        };
	    }
	    function transformedConnectorGeometryPoints(object, geometries) {
	        const seen = new Set();
	        const orderedGeometries = [...geometries].sort((left, right) => Number(right.hasDecodedBounds === true) - Number(left.hasDecodedBounds === true));
	        return orderedGeometries.flatMap((geometry) => geometry.pins.flatMap((pin) => {
	            const point = transformedConnectorPinPagePoint(object, pin, geometry.boundingBox);
	            const key = pagePointKey(point.x, point.y);
	            if (seen.has(key)) {
	                return [];
	            }
	            seen.add(key);
	            return [point];
	        }));
	    }
	    function decodedConnectorGeometryForPlacedSymbol(object, catalog) {
	        const sourceLibName = object.resolvedMetadata?.libName ?? '';
	        const identityGeometry = canonicalSourceLibraryName(sourceLibName) ?
	            catalog?.byIdentity.get(liveConnectorPinIdentityKey(object.symbolName, sourceLibName))
	            : undefined;
	        const symbolKey = liveConnectorPinSymbolKey(object.symbolName);
	        const exactGeometry = identityGeometry?.hasDecodedBounds === true ? identityGeometry : undefined;
	        const corroboratedSymbolGeometry = catalog?.bySymbol.get(symbolKey);
	        const candidateGeometry = exactGeometry ??
	            (corroboratedSymbolGeometry?.hasDecodedBounds === true ? corroboratedSymbolGeometry : undefined) ??
	            uniquelyTransformedConnectorGeometry(object, (catalog?.candidatesBySymbol.get(symbolKey) ?? []).filter((candidate) => candidate.hasDecodedBounds === true));
	        return candidateGeometry?.hasDecodedBounds === true ? candidateGeometry : undefined;
	    }
	    function uniquelyTransformedConnectorGeometry(object, candidates) {
	        const byPageGeometry = new Map();
	        for (const candidate of candidates) {
	            const key = candidate.pins
	                .map((pin) => {
	                const point = transformedConnectorPinPagePoint(object, pin, candidate.boundingBox);
	                return pagePointKey(point.x, point.y);
	            })
	                .join('|');
	            byPageGeometry.set(key, candidate);
	        }
	        return byPageGeometry.size === 1 ? byPageGeometry.values().next().value : undefined;
	    }
	    function transformedConnectorPinPagePoint(object, pin, boundingBox) {
	        const centerX = (boundingBox.X1 + boundingBox.X2) / 2;
	        const centerY = (boundingBox.Y1 + boundingBox.Y2) / 2;
	        let x = pin.hotptX - centerX;
	        const y = pin.hotptY - centerY;
	        const mirror = object.resolvedMetadata?.mirror ?? ((object.flags & 0x04) !== 0 ? 1 : 0);
	        if (mirror === 1) {
	            x = -x;
	        }
	        const rotation = object.resolvedMetadata?.rotation ?? object.flags & 0x03;
	        const rotated = rotation === 1 ? { x: y, y: -x }
	            : rotation === 2 ? { x: -x, y: -y }
	                : rotation === 3 ? { x: -y, y: x }
	                    : { x, y };
	        const placedCenter = rotation % 2 === 1 ?
	            { x: object.locX + centerY, y: object.locY + centerX }
	            : { x: object.locX + centerX, y: object.locY + centerY };
	        return { x: placedCenter.x + rotated.x, y: placedCenter.y + rotated.y };
	    }
	    function findAdjacentDrawnPortNetName(object, objects) {
	        let best;
	        const candidatePoints = [
	            { x: object.locX, y: object.locY },
	            { x: object.locX + 10, y: object.locY + 10 },
	            { x: object.locX + 10, y: object.locY + 20 },
	        ];
	        for (const drawnInst of objects.filter(isDrawnInst)) {
	            for (const port of drawnInst.materializedPortInstances ?? []) {
	                if (!port.name || port.isBus || port.isNoConnect) {
	                    continue;
	                }
	                const portX = drawnInst.locX + (port.hotptX ?? port.startX ?? 0);
	                const portY = drawnInst.locY + (port.hotptY ?? port.startY ?? 0);
	                const distance = Math.min(...candidatePoints.map((point) => Math.abs(point.x - portX) + Math.abs(point.y - portY)));
	                if (distance > 25 || (best && distance >= best.distance)) {
	                    continue;
	                }
	                best = { distance, name: port.name };
	            }
	        }
	        return best?.name;
	    }
	    function buildBusEntries(objects) {
	        const entries = [];
	        const seen = new Set();
	        for (const object of objects) {
	            if (object.kind !== 'bus-entry') {
	                continue;
	            }
	            const key = `${object.busX}:${object.busY}:${object.endX}:${object.endY}`;
	            if (seen.has(key)) {
	                continue;
	            }
	            seen.add(key);
	            entries.push({ BusX: object.busX, BusY: object.busY, EndX: object.endX, EndY: object.endY });
	        }
	        return entries;
	    }
	    function enrichDrawnInstPortsFromPageConnectivity(objects, diagnostics, strings = []) {
	        const drawnInsts = objects.filter(isDrawnInst);
	        const portInstances = objects.filter(isPortInstance);
	        if (drawnInsts.length !== 1 || portInstances.length === 0) {
	            return;
	        }
	        const drawnInst = drawnInsts[0];
	        if ((drawnInst.materializedPortInstances?.length ?? 0) >= portInstances.length) {
	            return;
	        }
	        const wireNameLookup = buildWireNetNameLookup(objects, strings);
	        const existingPortsByPoint = new Map();
	        for (const port of drawnInst.materializedPortInstances ?? []) {
	            existingPortsByPoint.set(coordKey(port.hotptX ?? port.startX ?? 0, port.hotptY ?? port.startY ?? 0), port);
	        }
	        const enrichedPorts = [...(drawnInst.materializedPortInstances ?? [])];
	        const boundary = drawnInstBoundaryFromPorts(drawnInst, enrichedPorts);
	        const portNameStrings = drawnInstPortNameStrings(drawnInst, strings);
	        for (let index = 0; index < portInstances.length; index++) {
	            const portInstance = portInstances[index];
	            const relX = portInstance.x - drawnInst.locX;
	            const relY = portInstance.y - drawnInst.locY;
	            const existing = existingPortsByPoint.get(coordKey(relX, relY));
	            if (existing) {
	                continue;
	            }
	            const wireName = portInstance.wireDbId !== undefined ? wireNameLookup.get(portInstance.wireDbId) : undefined;
	            const rawName = drawnInstPortStringName(portNameStrings, relX, relY, boundary, wireName) ?? wireName ?? portInstance.pinName;
	            if (rawName === undefined) {
	                emitBudgetedDiagnostic(diagnostics, `${drawnInst.dbId}\0${portInstance.pinIndex ?? index}`, 'sheet-port-name-undecoded', `sheetSymbol=${drawnInst.dbId} pinIndex=${portInstance.pinIndex ?? index} x=${relX} y=${relY}`, 'CFB sheet symbol', 'warning');
	                continue;
	            }
	            const name = rawName;
	            enrichedPorts.push({
	                name,
	                position: portInstance.position ?? index,
	                pinIndex: portInstance.pinIndex,
	                startX: relX,
	                startY: relY,
	                hotptX: relX,
	                hotptY: relY,
	                x: portInstance.x,
	                y: portInstance.y,
	                ...(portInstance.wireDbId !== undefined ? { wireDbId: portInstance.wireDbId } : {}),
	                isNoConnect: portInstance.isNoConnect,
	                isBus: isExplicitBusAliasName(rawName) || rawName.includes('['),
	                type: portInstance.type ?? inferredDrawnInstPortType(relX, relY, boundary),
	                visible: portInstance.visible,
	                isPlacedInstPin: false,
	                source: 'record',
	                isExplicit: true,
	            });
	            existingPortsByPoint.set(coordKey(relX, relY), enrichedPorts[enrichedPorts.length - 1]);
	        }
	        addMissingDrawnInstBoundaryPorts(drawnInst, objects.filter(isWire), wireNameLookup, portNameStrings, existingPortsByPoint, enrichedPorts, diagnostics);
	        drawnInst.materializedPortInstances = enrichedPorts;
	    }
	    function buildWireNetNameLookup(objects, strings = []) {
	        const wires = objects.filter(isWire);
	        const aliases = objects.filter(isAlias);
	        const busEntryCandidates = identifyBusEntryCandidates(wires, aliases);
	        const busEntryWireIds = new Set(busEntryCandidates.map((candidate) => candidate.wire.dbId));
	        const metadataNetNames = buildMetadataNetNameLookup(strings, objects);
	        const netNameCarriers = buildWireNetNameCarriers(objects);
	        const names = new Map();
	        for (const candidate of busEntryCandidates) {
	            names.set(candidate.wire.dbId, candidate.alias.name);
	        }
	        const wireState = new Map();
	        for (const wire of wires.filter((wire) => !busEntryWireIds.has(wire.dbId))) {
	            wireState.set(wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] });
	        }
	        for (const alias of aliases) {
	            const owner = findExactAliasOwner(alias, wires);
	            if (!owner || busEntryWireIds.has(owner.dbId)) {
	                continue;
	            }
	            wireState.get(owner.dbId)?.aliases.push(alias);
	            names.set(owner.dbId, alias.name);
	        }
	        for (const group of groupConnectedWires([...wireState.values()].map((state) => state.wire), wireState)) {
	            const metadataName = netNameForWireGroup(group, group.flatMap((wire) => wireState.get(wire.dbId)?.aliases ?? []), netNameCarriers, metadataNetNames);
	            if (!metadataName) {
	                continue;
	            }
	            for (const wire of group) {
	                names.set(wire.dbId, metadataName);
	            }
	        }
	        return names;
	    }
	    function addMissingDrawnInstBoundaryPorts(drawnInst, wires, wireNameLookup, portNameStrings, existingPortsByPoint, enrichedPorts, diagnostics) {
	        const boundary = drawnInstBoundaryFromPorts(drawnInst, enrichedPorts);
	        for (const wire of wires) {
	            const rawName = wireNameLookup.get(wire.dbId);
	            if (!rawName) {
	                continue;
	            }
	            for (const point of [
	                { x: wire.startX, y: wire.startY },
	                { x: wire.endX, y: wire.endY },
	            ]) {
	                const relX = point.x - drawnInst.locX;
	                const relY = point.y - drawnInst.locY;
	                const key = coordKey(relX, relY);
	                if (existingPortsByPoint.has(key) || !isPointOnDrawnInstBoundary(relX, relY, boundary)) {
	                    continue;
	                }
	                const stringName = drawnInstPortStringName(portNameStrings, relX, relY, boundary, rawName);
	                const name = stringName ?? rawName;
	                const port = {
	                    name,
	                    position: enrichedPorts.length,
	                    startX: relX,
	                    startY: relY,
	                    hotptX: relX,
	                    hotptY: relY,
	                    x: point.x,
	                    y: point.y,
	                    wireDbId: wire.dbId,
	                    isNoConnect: false,
	                    isBus: isExplicitBusAliasName(stringName ?? rawName) || (stringName ?? rawName).includes('['),
	                    type: inferredDrawnInstPortType(relX, relY, boundary),
	                    visible: true,
	                    isPlacedInstPin: false,
	                    source: 'wire-boundary',
	                    isExplicit: false,
	                };
	                emitBudgetedDiagnostic(diagnostics, `${drawnInst.dbId}\0${key}`, 'sheet-port-synthesized-from-wire', `sheetSymbol=${drawnInst.dbId} name="${name}" x=${relX} y=${relY} wire=${wire.dbId}`, 'CFB sheet symbol', 'warning');
	                enrichedPorts.push(port);
	                existingPortsByPoint.set(key, port);
	            }
	        }
	    }
	    function drawnInstPortNameStrings(drawnInst, strings) {
	        return strings.filter((entry) => entry.offset > drawnInst.offset &&
	            entry.followingUInt32Le !== undefined &&
	            entry.followingUInt32Le <= Math.max(drawnInst.relativeDisplayX2, drawnInst.relativeDisplayY2) &&
	            isPlausibleDrawnPortName(entry.value));
	    }
	    function drawnInstPortStringName(portNameStrings, relX, relY, boundary, fallbackName) {
	        if (relY === boundary.Y1) {
	            const coordinateName = portNameStrings.find((entry) => entry.followingUInt32Le === relX);
	            if (coordinateName) {
	                return coordinateName.value;
	            }
	        }
	        if (fallbackName) {
	            const matchingName = portNameStrings.find((entry) => entry.followingUInt32Le === relX && entry.value === fallbackName);
	            if (matchingName) {
	                return matchingName.value;
	            }
	        }
	        return undefined;
	    }
	    function isPlausibleDrawnPortName(value) {
	        return /^[A-Za-z0-9_+.\-#[\]/]+$/.test(value);
	    }
	    function inferredDrawnInstPortType(relX, relY, boundary) {
	        if (relY === boundary.Y2) {
	            return 1;
	        }
	        if (relY === boundary.Y1) {
	            return 0;
	        }
	        if (relX === boundary.X1 || relX === boundary.X2) {
	            return 1;
	        }
	        return DEFAULT_PIN_TYPE;
	    }
	    function enrichDrawnInstReferences(objects, diagnostics, pageName) {
	        for (const drawnInst of objects.filter(isDrawnInst)) {
	            if (drawnInst.reference || drawnInst.deviceDesignator || drawnInst.name) {
	                continue;
	            }
	            emitPageReferenceDiagnostic(diagnostics, pageName, `drawn-inst-reference-${drawnInst.dbId}`, 'sheet-symbol-reference-unrecorded', `sheet symbol dbId=${drawnInst.dbId} carries no reference, designator or name of its own, so it is left unnamed`);
	        }
	    }
	    function drawnInstBoundaryFromPorts(drawnInst, ports) {
	        const portXs = ports.map((port) => port.hotptX ?? port.startX ?? 0);
	        const portYs = ports.map((port) => port.hotptY ?? port.startY ?? 0);
	        const maxPortY = Math.max(0, ...portYs);
	        let y2 = Math.max(0, ...portYs, Math.floor(drawnInst.relativeDisplayY2 / 10) * 10);
	        if (y2 - maxPortY <= 10) {
	            y2 = maxPortY;
	        }
	        return {
	            X1: Math.min(0, ...portXs),
	            X2: Math.max(0, ...portXs, Math.floor(drawnInst.relativeDisplayX2 / 10) * 10),
	            Y1: Math.min(0, ...portYs),
	            Y2: y2,
	        };
	    }
	    function isPointOnDrawnInstBoundary(relX, relY, boundary) {
	        return (relX >= boundary.X1 &&
	            relX <= boundary.X2 &&
	            relY >= boundary.Y1 &&
	            relY <= boundary.Y2 &&
	            (relX === boundary.X1 || relX === boundary.X2 || relY === boundary.Y1 || relY === boundary.Y2));
	    }
	    function identifyBusEntryWireIds(wires, aliases) {
	        return new Set(identifyBusEntryCandidates(wires, aliases).map((candidate) => candidate.wire.dbId));
	    }
	    function identifyBusEntryCandidates(wires, aliases) {
	        const busSpineWireIds = findBusSpineWireIds(wires);
	        const busSpineGroupKeys = buildBusSpineGroupKeyLookup(wires, busSpineWireIds);
	        const explicitBusAliasNames = aliases.map((alias) => alias.name).filter(isExplicitBusAliasName);
	        const candidateSets = [];
	        for (const alias of aliases) {
	            const owner = findExactAliasOwner(alias, wires);
	            if (!owner || isBusWire(owner) || !isBusEntryMemberName(alias.name, explicitBusAliasNames)) {
	                continue;
	            }
	            const candidates = findBusEntryPointCandidatesForWire(owner, wires, busSpineWireIds, busSpineGroupKeys);
	            if (candidates.length > 0) {
	                candidateSets.push({ wire: owner, alias, candidates });
	            }
	        }
	        const preferredDirections = new Map();
	        for (const candidateSet of candidateSets) {
	            const candidates = preferredBusEntryCandidates(candidateSet.candidates);
	            const selected = candidates[0];
	            if (selected && !preferredDirections.has(selected.groupSideKey)) {
	                preferredDirections.set(selected.groupSideKey, selected.direction);
	            }
	        }
	        return candidateSets.map((candidateSet) => {
	            const candidates = preferredBusEntryCandidates(candidateSet.candidates);
	            const selected = candidates.find((candidate) => preferredDirections.get(candidate.groupSideKey) === candidate.direction) ??
	                candidates[0];
	            return { wire: candidateSet.wire, alias: candidateSet.alias, busEntry: selected.busEntry };
	        });
	    }
	    function findComponentPinAttachedWireIds(wires, placedSymbols) {
	        const ids = new Set();
	        for (const wire of wires) {
	            if (wireHasPlacedSymbolPinAttachment(wire, placedSymbols)) {
	                ids.add(wire.dbId);
	            }
	        }
	        return ids;
	    }
	    function wireHasPlacedSymbolPinAttachment(wire, placedSymbols) {
	        for (const placedSymbol of placedSymbols) {
	            for (const port of placedSymbol.materializedPortInstances ?? []) {
	                const x = placedSymbol.locX + (port.hotptX ?? port.startX ?? 0);
	                const y = placedSymbol.locY + (port.hotptY ?? port.startY ?? 0);
	                if (pointOnWire(x, y, wire)) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    function findBusEntryPointCandidatesForWire(wire, wires, busSpineWireIds, busSpineGroupKeys) {
	        const candidates = [];
	        for (const endpoint of [
	            { x: wire.startX, y: wire.startY },
	            { x: wire.endX, y: wire.endY },
	        ]) {
	            for (const busPoint of busEntryBusPointCandidates(endpoint.x, endpoint.y)) {
	                const busWire = wires.find((candidate) => candidate.dbId !== wire.dbId &&
	                    busSpineWireIds.has(candidate.dbId) &&
	                    pointOnWire(busPoint.x, busPoint.y, candidate));
	                if (busWire) {
	                    const entry = normalizeBusEntry({ BusX: busPoint.x, BusY: busPoint.y, EndX: endpoint.x, EndY: endpoint.y });
	                    candidates.push({
	                        busEntry: entry,
	                        busWire,
	                        groupSideKey: busEntryGroupSideKey(busSpineGroupKeys, busWire, entry),
	                        direction: Math.sign(entry.BusY - entry.EndY),
	                        isEndpoint: isWireEndpoint(busWire, busPoint.x, busPoint.y),
	                    });
	                }
	            }
	        }
	        return candidates;
	    }
	    function preferredBusEntryCandidates(candidates) {
	        const endpointCandidates = candidates.filter((candidate) => candidate.isEndpoint);
	        return endpointCandidates.length > 0 ? endpointCandidates : [...candidates];
	    }
	    function normalizeBusEntry(entry, _busWire) {
	        return entry;
	    }
	    function buildBusSpineGroupKeyLookup(wires, busSpineWireIds) {
	        const busSpineWires = wires.filter((wire) => busSpineWireIds.has(wire.dbId));
	        const groups = groupConnectedWires(busSpineWires, new Map(busSpineWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
	        const keys = new Map();
	        for (const group of groups) {
	            const key = group
	                .map((wire) => wire.dbId)
	                .sort((left, right) => left - right)
	                .join(',');
	            for (const wire of group) {
	                keys.set(wire.dbId, key);
	            }
	        }
	        return keys;
	    }
	    function busEntryGroupSideKey(busSpineGroupKeys, busWire, entry) {
	        return `${busSpineGroupKeys.get(busWire.dbId) ?? busWire.dbId}:${Math.sign(entry.BusX - entry.EndX)}`;
	    }
	    function findBusSpineWireIds(wires) {
	        const nonAliasWires = wires.filter((wire) => wire.wireType !== 11);
	        const groups = groupConnectedWires(nonAliasWires, new Map(nonAliasWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
	        const ids = new Set();
	        for (const group of groups) {
	            if (group.some(isBusWire)) {
	                for (const wire of group) {
	                    ids.add(wire.dbId);
	                }
	            }
	        }
	        return ids;
	    }
	    function identifyLogicalBusWireIds(wires, aliases, busEntryCandidates) {
	        const ids = findBusSpineWireIdsFromBusEntries(wires, busEntryCandidates);
	        const nonAliasWires = wires.filter((wire) => wire.wireType !== 11);
	        const nonAliasGroups = groupConnectedWires(nonAliasWires, new Map(nonAliasWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
	        for (const alias of aliases) {
	            if (!isExplicitBusAliasName(alias.name)) {
	                continue;
	            }
	            const owner = findExactAliasOwner(alias, wires);
	            if (!owner) {
	                continue;
	            }
	            ids.add(owner.dbId);
	            for (const group of nonAliasGroups) {
	                if (group.some((wire) => wiresTouch(owner, wire))) {
	                    for (const wire of group) {
	                        ids.add(wire.dbId);
	                    }
	                }
	            }
	        }
	        return ids;
	    }
	    function findBusSpineWireIdsFromBusEntries(wires, busEntryCandidates) {
	        const nonAliasWires = wires.filter((wire) => wire.wireType !== 11);
	        const groups = groupConnectedWires(nonAliasWires, new Map(nonAliasWires.map((wire) => [wire.dbId, { wire, aliases: [], groupNames: [], junctions: [] }])));
	        const ids = new Set();
	        for (const group of groups) {
	            if (busEntryCandidates.some((candidate) => group.some((wire) => pointOnWire(candidate.busEntry.BusX, candidate.busEntry.BusY, wire)))) {
	                for (const wire of group) {
	                    ids.add(wire.dbId);
	                }
	            }
	        }
	        return ids;
	    }
	    function isExplicitBusAliasName(name) {
	        return /^[^\s=\[\]]+\[[^\s=\[\]]+\]$/.test(name.trim());
	    }
	    function recordedPlacementReference(object, fallbacks) {
	        const recorded = object.referenceTail?.reference ??
	            object.resolvedMetadata?.reference ??
	            fallbacks.find((fallback) => fallback !== undefined);
	        const reference = recorded ?? '?';
	        return { reference, unannotated: reference.endsWith('?'), recorded: recorded !== undefined };
	    }
	    function buildMultipartSectionSuffixLookup(document, placedPartCacheIdentityCatalog, sectionCounts, diagnostics, occurrenceTrust = document.hierarchyOccurrenceTrust ??
	        (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)(document)) {
	        const suffixes = new WeakMap();
	        const referenceResolutions = new WeakMap();
	        const referenceRows = new Map();
	        const occurrenceSelectors = new Map();
	        const library = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
	        const libraryStrings = library?.kind === 'library' ? library.stringCatalog : undefined;
	        for (const { streamPath, entry } of occurrenceTrust.rejectedEntries) {
	            if (entry.ownershipState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved &&
	                entry.sectionSelectorStringIndex === undefined) {
	                emitDecisionDiagnostic(diagnostics, 'occurrence-selector-withheld', `stream=${JSON.stringify(streamPath)}, offset=${entry.offset}, refInstId=${entry.refInstId ?? entry.dbId}: incomplete occurrence annotation rejects branch trust`, 'CFB hierarchy occurrence', 'warning');
	            }
	            emitDecisionDiagnostic(diagnostics, 'occurrence-ownership-withheld', `stream=${JSON.stringify(streamPath)}, offset=${entry.offset}, refInstId=${entry.refInstId ?? entry.dbId}: branch trust is withheld; no occurrence annotation supplied`, 'CFB hierarchy occurrence', 'warning');
	        }
	        for (const stream of document.streams) {
	            if (stream.parsed?.kind !== 'hierarchy') {
	                continue;
	            }
	            for (const entry of stream.parsed.entries) {
	                if (entry.parentBranchOffset === undefined &&
	                    entry.referenceStringOffset !== undefined &&
	                    entry.owningViewName !== undefined) {
	                    const key = JSON.stringify([entry.owningViewName.toUpperCase(), entry.refInstId ?? entry.dbId]);
	                    const rows = referenceRows.get(key) ?? [];
	                    rows.push({ entry, streamPath: stream.path });
	                    referenceRows.set(key, rows);
	                }
	                if (!occurrenceTrust.trustedEntries.has(entry)) {
	                    continue;
	                }
	                const id = entry.refInstId ?? entry.dbId;
	                if (id === undefined || entry.owningViewName === undefined || entry.sectionSelectorStringIndex === undefined) {
	                    continue;
	                }
	                const pageKey = JSON.stringify([entry.owningViewName.toUpperCase(), id]);
	                const selectors = occurrenceSelectors.get(pageKey) ?? [];
	                selectors.push({
	                    selector: libraryStrings?.[entry.sectionSelectorStringIndex],
	                    index: entry.sectionSelectorStringIndex,
	                    reference: entry.name,
	                });
	                occurrenceSelectors.set(pageKey, selectors);
	            }
	        }
	        const streamsByView = new Map();
	        for (const [stream, views] of occurrenceTrust.viewsByStream) {
	            for (const view of views) {
	                const streams = streamsByView.get(view) ?? [];
	                streams.push(stream);
	                streamsByView.set(view, streams);
	            }
	        }
	        const placements = [];
	        for (const stream of document.streams) {
	            const parsed = stream.parsed;
	            if (parsed?.kind !== 'page') {
	                continue;
	            }
	            const objects = stream.decodedPageObjects ?? [];
	            for (const object of objects) {
	                if (object.kind !== 'placed-symbol' || object.role !== 'part-inst') {
	                    continue;
	                }
	                placements.push({
	                    object,
	                    strings: stream.lengthPrefixedStrings,
	                    viewName: parsed.viewName,
	                });
	            }
	        }
	        for (const placement of placements) {
	            if (placement.object.referenceState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld) {
	                suffixes.set(placement.object, { state: MultipartSectionState.Withheld });
	                emitDecisionDiagnostic(diagnostics, 'placement-reference-connectivity-withheld', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: recorded reference was rejected; withholding pin connections`, 'CFB physical section', 'warning');
	                continue;
	            }
	            const pageKey = JSON.stringify([placement.viewName.toUpperCase(), placement.object.dbId]);
	            const declaredSections = declaredSectionCount(placement.object, placedPartCacheIdentityCatalog, sectionCounts);
	            const { unannotated } = recordedPlacementReference(placement.object, [placement.object.reference]);
	            const streams = streamsByView.get(placement.viewName.toUpperCase()) ?? [];
	            const scopeVetoed = streams.some((stream) => withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream, placement.viewName, placement.object.dbId, 'placement-annotation'));
	            const vetoed = scopeVetoed ||
	                streams.some((stream) => withholdHierarchyAdoption(diagnostics, occurrenceTrust, stream, placement.viewName, placement.object.dbId, 'section-selector', undefined, undefined, OrcadDsnCfbOccurrenceOwnership_1.HierarchyAdoptionValue.Section));
	            if (unannotated) {
	                const rows = referenceRows.get(pageKey) ?? [];
	                const pathVetoed = !scopeVetoed &&
	                    rows.some(({ entry, streamPath }) => withholdHierarchyAdoption(diagnostics, occurrenceTrust, streamPath, placement.viewName, placement.object.dbId, 'page-instance-unannotated', entry.ownershipBranchOffset));
	                if (scopeVetoed || pathVetoed || rows.some(({ entry }) => !occurrenceTrust.trustedEntries.has(entry))) {
	                    referenceResolutions.set(placement.object, { state: MultipartSectionState.ReferenceWithheld });
	                    emitDecisionDiagnostic(diagnostics, 'occurrence-reference-unresolved', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: occurrence trust or same-path conflict withholds the page annotation`, 'CFB reference', 'warning');
	                }
	                else if (rows.every(({ entry }) => entry.name === undefined || entry.name === '')) {
	                    referenceResolutions.set(placement.object, { state: MultipartSectionState.NoOccurrenceAnnotation });
	                    emitDecisionDiagnostic(diagnostics, 'occurrence-reference-unavailable', `dbId=${placement.object.dbId}: no tagged occurrence annotation; no reference or section override supplied`, 'CFB reference', 'info');
	                }
	                else {
	                    const reference = rows[0] === undefined ? undefined : (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyReferenceCandidate)(rows[0].entry)?.reference;
	                    const agrees = reference !== undefined &&
	                        rows.every(({ entry }) => (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyReferenceCandidate)(entry)?.reference === reference);
	                    emitDecisionDiagnostic(diagnostics, agrees ? 'page-instance-unannotated' : 'page-instance-reference-varies', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: ${agrees ? 'page reference remains unannotated; agreeing references belong only to their occurrences' : 'independent occurrence references do not agree; page reference remains unresolved'}; paths=${JSON.stringify(rows.map(({ entry, streamPath }) => ({ path: [streamPath, entry.ownershipBranchOffset, entry.occurrenceId], reference: entry.name })))}`, 'CFB reference', 'info');
	                }
	            }
	            if (vetoed) {
	                suffixes.set(placement.object, declaredSections === undefined || declaredSections < 2 ?
	                    { state: MultipartSectionState.NoOccurrenceAnnotation }
	                    : { state: MultipartSectionState.Withheld });
	                emitDecisionDiagnostic(diagnostics, declaredSections === undefined || declaredSections < 2 ?
	                    'occurrence-reference-unresolved'
	                    : 'occurrence-section-unresolved', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: occurrence ownership is unresolved; withholding occurrence-derived placement evidence`, declaredSections === undefined || declaredSections < 2 ? 'CFB reference' : 'CFB physical section', 'warning');
	                continue;
	            }
	            const selectors = occurrenceSelectors.get(pageKey) ?? [];
	            if (selectors.length === 0 && declaredSections !== undefined && declaredSections > 1) {
	                suffixes.set(placement.object, { state: MultipartSectionState.Withheld });
	                emitDecisionDiagnostic(diagnostics, 'occurrence-section-unresolved', `view=${JSON.stringify(placement.viewName)}, dbId=${placement.object.dbId}: no complete branch ownership and nonempty occurrence set; withholding multi-section pin connections`, 'CFB physical section', 'warning');
	                continue;
	            }
	            if (declaredSections === undefined || declaredSections < 2) {
	                continue;
	            }
	            const identity = placedPartCacheIdentityCatalog.get(placement.object);
	            const nonempty = selectors.filter((candidate) => candidate.selector !== '');
	            const recorded = placement.object.resolvedMetadata?.deviceDesignator;
	            const occurrence = nonempty[0] ?? selectors[0];
	            const occurrenceOverridesPage = unannotated && nonempty.length > 0;
	            if (unannotated) {
	                emitDecisionDiagnostic(diagnostics, 'page-instance-unannotated', `dbId=${placement.object.dbId}: page reference remains unannotated; evaluating recorded occurrence section; occurrence references=${JSON.stringify(selectors.map((candidate) => candidate.reference))}`, 'CFB physical section', 'info');
	            }
	            const index = multipartSectionIndexFromMarker(placement.object, placement.strings);
	            const sections = document.streams.flatMap((stream) => (stream.cacheSourceLibraries ?? []).flatMap((source) => (identity !== undefined &&
	                identity !== null &&
	                source.physicalPackageName === identity.packageName &&
	                canonicalSourceLibraryName(source.library.value) === canonicalSourceLibraryName(identity.cacheSourceLibName)) ?
	                (source.physicalSections ?? []).filter((section) => stripNormalViewSuffix(section.cellName) === identity.cellName)
	                : []));
	            const markerSections = sections.filter((section) => index !== undefined && sectionIndexMatchesDesignator(section.selector, index));
	            const markerDesignators = new Set(markerSections.map((section) => section.selector));
	            const markerDesignator = markerDesignators.size === 1 ? markerSections[0]?.selector : undefined;
	            const selector = occurrenceOverridesPage ? occurrence?.selector
	                : recorded?.length ? recorded
	                    : markerDesignator;
	            const selectedSections = sections.filter((section) => section.selector === selector);
	            const agrees = nonempty.every((candidate) => candidate.selector !== undefined && candidate.selector === selector) &&
	                (occurrenceOverridesPage ||
	                    index === undefined ||
	                    (index < declaredSections && (selector === markerDesignator || sections.length === 0))) &&
	                (selectedSections.length === 0 ||
	                    selectedSections.every((section) => arraysEqual(section.pinNumbers, selectedSections[0].pinNumbers)));
	            const accepted = (selector !== undefined || (index !== undefined && sections.length === 0 && nonempty.length === 0)) &&
	                (recorded === undefined || recorded === '' || selectedSections.length > 0) &&
	                (!occurrenceOverridesPage || selectedSections.length > 0) &&
	                agrees;
	            suffixes.set(placement.object, accepted ?
	                {
	                    state: MultipartSectionState.Resolved,
	                    designator: selector ?? `-${(index ?? 0) + 1}`,
	                }
	                : { state: MultipartSectionState.Withheld });
	            emitDecisionDiagnostic(diagnostics, accepted ? 'occurrence-section-selected' : 'occurrence-section-unresolved', `dbId=${placement.object.dbId} recorded=${JSON.stringify(recorded)} marker=${index} selectors=${JSON.stringify(selectors)}: ${accepted ? 'using recorded section evidence with agreeing corroboration' : 'withholding pin connections without agreeing recorded section and source/view evidence'}`, 'CFB physical section', accepted ? 'info' : 'warning');
	        }
	        for (const { object } of placements) {
	            const reference = referenceResolutions.get(object);
	            if (reference === undefined) {
	                continue;
	            }
	            const section = suffixes.get(object);
	            if (section === undefined || section.state === MultipartSectionState.NoOccurrenceAnnotation) {
	                suffixes.set(object, reference);
	            }
	        }
	        return suffixes;
	    }
	    function multipartSectionIndexFromMarker(object, strings) {
	        const frame = object.recordFrame;
	        if (!frame) {
	            return undefined;
	        }
	        const markers = strings.filter((string) => string.offset >= object.offset &&
	            string.offset >= frame.offset &&
	            string.offset + 2 + string.length + 1 + 2 === frame.endOffset &&
	            string.hasNullTerminator &&
	            stripNormalViewSuffix(string.value) === stripNormalViewSuffix(object.graphicName) &&
	            string.followingUInt32Le !== undefined);
	        const indices = markers.map((marker) => (marker.followingUInt32Le ?? 0) & 0xffff);
	        return indices.length > 0 && indices.every((index) => index === indices[0]) ? indices[0] : undefined;
	    }
	    function declaredSectionCount(object, placedPartCacheIdentityCatalog, sectionCounts) {
	        const identity = placedPartCacheIdentityCatalog.get(object);
	        if (!identity) {
	            return undefined;
	        }
	        return sectionCounts.get(cacheIdentityKey(identity.cellName, identity.packageName, identity.cacheSourceLibName));
	    }
	    function buildPageReferenceLookup(strings, objects, cacheCatalog, placedPartCacheIdentityCatalog, diagnostics, pageName) {
	        const partsByDbId = new Map(objects.filter(isPlacedSymbolRole('part-inst')).map((object) => [object.dbId, object]));
	        const netNames = new Set(objects.filter(isAlias).map((alias) => alias.name.toUpperCase()));
	        const wireMetadataIds = new Set(objects.filter(isWire).map((wire) => wire.metadataId));
	        for (const [metadataId, name] of buildMetadataNetNameLookup(strings, objects)) {
	            if (wireMetadataIds.has(metadataId)) {
	                netNames.add(name.toUpperCase());
	            }
	        }
	        const pageStringsByDbId = new Map();
	        for (let index = 0; index < strings.length; index++) {
	            const string = strings[index];
	            if (!isPotentialPageReferenceName(string.value)) {
	                continue;
	            }
	            const dbId = precedingPageGraphicDbId(strings, index);
	            const object = dbId === undefined ? undefined : partsByDbId.get(dbId);
	            if (dbId === undefined || object === undefined) {
	                continue;
	            }
	            const values = pageStringsByDbId.get(dbId) ?? new Set();
	            values.add(string.value);
	            pageStringsByDbId.set(dbId, values);
	        }
	        const uniqueCandidates = new Map();
	        for (const [dbId, values] of pageStringsByDbId) {
	            const object = partsByDbId.get(dbId);
	            if (object === undefined) {
	                continue;
	            }
	            const recorded = recordedPlacementReference(object, [object.reference]);
	            if (recorded.recorded) {
	                emitDecisionDiagnostic(diagnostics, 'reference-recovery-withheld', `page=${JSON.stringify(pageName)} dbId=${dbId} source=page-string candidates=${JSON.stringify([...values])} recorded=${JSON.stringify(recorded.reference)}: recorded page evidence takes precedence`, 'CFB page reference', 'info');
	                continue;
	            }
	            const candidates = new Map([...values]
	                .filter((value) => isValidPageReferenceForPlacedPart(value, object, cacheCatalog, placedPartCacheIdentityCatalog, netNames))
	                .map((value) => [value.toUpperCase(), value]));
	            if (candidates.size === 0) {
	                continue;
	            }
	            const prefixMatches = object ?
	                Array.from(candidates.values()).filter((candidate) => pageReferenceMatchesCatalogPrefix(candidate, object, cacheCatalog, placedPartCacheIdentityCatalog))
	                : [];
	            const candidate = candidates.size === 1 ? candidates.values().next().value
	                : prefixMatches.length === 1 ? prefixMatches[0]
	                    : undefined;
	            if (candidate === undefined) {
	                emitPageReferenceDiagnostic(diagnostics, pageName, String(dbId), 'page-reference-conflict', `page="${pageName}" dbId=${dbId} candidates=${Array.from(candidates.values()).join(',')}`);
	                continue;
	            }
	            uniqueCandidates.set(dbId, candidate);
	        }
	        const ownersByReference = new Map();
	        for (const object of partsByDbId.values()) {
	            const { reference, unannotated } = recordedPlacementReference(object, [
	                object.reference,
	                uniqueCandidates.get(object.dbId),
	            ]);
	            if (reference.length === 0 || unannotated) {
	                continue;
	            }
	            const owners = ownersByReference.get(reference.toUpperCase()) ?? [];
	            owners.push(object);
	            ownersByReference.set(reference.toUpperCase(), owners);
	        }
	        const references = new Map();
	        for (const [dbId, reference] of uniqueCandidates) {
	            const owners = ownersByReference.get(reference.toUpperCase()) ?? [];
	            if (owners.length > 1 && !areMultipartReferencePeers(owners, placedPartCacheIdentityCatalog)) {
	                emitPageReferenceDiagnostic(diagnostics, pageName, reference.toUpperCase(), 'duplicate-page-reference', `page="${pageName}" dbId=${dbId} reference="${reference}" dbIds=${owners.map((owner) => owner.dbId).join(',')}`);
	                continue;
	            }
	            references.set(dbId, reference);
	            emitPageReferenceDiagnostic(diagnostics, pageName, String(dbId), 'reference-recovered', `page="${pageName}" dbId=${dbId} source=page-string reference="${reference}"`, 'info');
	        }
	        return references;
	    }
	    function isValidPageReferenceForPlacedPart(candidate, object, cacheCatalog, placedPartCacheIdentityCatalog, netNames) {
	        const normalizedCandidate = candidate.toUpperCase();
	        if (netNames.has(normalizedCandidate) || isPageReferencePlaceholder(candidate)) {
	            return false;
	        }
	        const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
	        const entries = cacheIdentity ? [cacheIdentity.catalogEntry] : catalogEntriesForGraphicName(cacheCatalog, object.graphicName);
	        const forbiddenValues = new Set([
	            object.symbolName,
	            object.graphicName,
	            stripNormalViewSuffix(object.symbolName),
	            stripNormalViewSuffix(object.graphicName),
	            object.partValue,
	            object.resolvedMetadata?.name,
	            object.resolvedMetadata?.partValue,
	            object.resolvedMetadata?.pkgName,
	            ...entries.flatMap((entry) => [entry.cellName, entry.packageName, entry.partValue]),
	            ...(object.resolvedMetadata?.userProperties ?? []).map((property) => property.value),
	            ...(object.userProperties ?? []).map((property) => property.value),
	        ]
	            .filter((value) => value !== undefined && value.length > 0)
	            .map((value) => stripNormalViewSuffix(value).toUpperCase()));
	        if (forbiddenValues.has(stripNormalViewSuffix(candidate).toUpperCase())) {
	            return false;
	        }
	        return true;
	    }
	    function pageReferenceMatchesCatalogPrefix(candidate, object, cacheCatalog, placedPartCacheIdentityCatalog) {
	        const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
	        const entries = cacheIdentity ? [cacheIdentity.catalogEntry] : catalogEntriesForGraphicName(cacheCatalog, object.graphicName);
	        const prefixes = new Set(entries
	            .map((entry) => entry.referencePrefix)
	            .filter((prefix) => prefix !== undefined && prefix.length > 0)
	            .map((prefix) => prefix.toUpperCase()));
	        if (prefixes.size !== 1) {
	            return false;
	        }
	        const prefix = prefixes.values().next().value;
	        return (prefix !== undefined && candidate.toUpperCase().startsWith(prefix) && /^\d/.test(candidate.slice(prefix.length)));
	    }
	    function areMultipartReferencePeers(objects, placedPartCacheIdentityCatalog) {
	        const identities = objects.map((object) => placedPartCacheIdentityCatalog.get(object));
	        if (identities.some((identity) => identity === undefined)) {
	            return false;
	        }
	        const resolvedIdentities = identities.filter((identity) => identity !== undefined);
	        const packageKeys = new Set(resolvedIdentities.map((identity) => packageSourceKey(identity.catalogEntry.sectionParentName ?? identity.packageName, identity.sourceLibName)));
	        const catalogBackedPeers = packageKeys.size === 1 &&
	            resolvedIdentities.every((identity) => identity.catalogEntry.deviceDesignator !== undefined ||
	                identity.catalogEntry.sectionParentName !== undefined ||
	                identity.catalogEntry.preserveSectionPackageIdentity === true);
	        return catalogBackedPeers || arePhysicalMultipartPackagePeers(objects, resolvedIdentities);
	    }
	    function arePhysicalMultipartPackagePeers(objects, identities) {
	        const sourceLibraries = new Set(identities.map((identity) => identity.canonicalSourceLibName));
	        const packageNames = identities.map((identity) => identity.packageName);
	        const packageNameSet = new Set(packageNames.map((name) => name.toUpperCase()));
	        const sectionStemCandidates = packageNames.map((name) => /^(.+)[A-Z]$/i.exec(name)?.[1]?.toUpperCase());
	        const sectionStems = new Set(sectionStemCandidates.filter((name) => name !== undefined));
	        const partNumbers = new Set(objects
	            .map((object) => physicalPartNumberForPlacedObject(object)?.toUpperCase())
	            .filter((partNumber) => partNumber !== undefined));
	        return (sourceLibraries.size === 1 &&
	            packageNameSet.size === objects.length &&
	            sectionStemCandidates.every((stem) => stem !== undefined) &&
	            sectionStems.size === 1 &&
	            partNumbers.size === 1 &&
	            objects.every((object) => physicalPartNumberForPlacedObject(object) !== undefined));
	    }
	    function physicalPartNumberForPlacedObject(object) {
	        return [...(object.resolvedMetadata?.userProperties ?? []), ...(object.userProperties ?? [])].find((property) => /^(?:Part Number|Part_Number|APN)$/i.test(property.name))?.value;
	    }
	    function emitPageReferenceDiagnostic(diagnostics, pageName, dedupeKey, reason, detail, severity = 'warning') {
	        if (reason === 'page-reference-conflict' || reason === 'duplicate-page-reference') {
	            emitDecisionDiagnostic(diagnostics, reason, detail, 'CFB page reference', severity);
	            return;
	        }
	        emitBudgetedDiagnostic(diagnostics, `${pageName}\0${dedupeKey}`, reason, detail, 'CFB page reference', severity);
	    }
	    function applySheetReferenceDisplayOverrides(partInsts, strings, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences) {
	        const labels = sheetReferenceDisplayLabels(strings);
	        if (labels.length === 0) {
	            return;
	        }
	        const claimedPartInsts = new Set();
	        for (const label of labels) {
	            const match = nearestSheetPartInstForDisplayLabel(label, partInsts, partInstanceOccurrenceReferences, claimedPartInsts);
	            if (!match) {
	                continue;
	            }
	            const existingReference = partInstanceOccurrenceReferences.get(match);
	            if (!existingReference) {
	                continue;
	            }
	            const hierarchyReference = hierarchySheetReferenceForDisplayLabel(hierarchySheetReferenceCatalog, label.reference, existingReference) ??
	                existingReference;
	            partInstanceOccurrenceReferences.set(match, {
	                ...hierarchyReference,
	                reference: label.reference,
	                dbId: match.DbId,
	            });
	            claimedPartInsts.add(match);
	        }
	    }
	    function sheetReferenceDisplayLabels(strings) {
	        const labels = [];
	        for (const string of strings) {
	            const reference = sheetReferenceFromRefinLabel(string.value);
	            const x = string.precedingInt32Le20;
	            const y = string.precedingInt32Le16;
	            if (!reference || x === undefined || y === undefined || !isPlausiblePageLabelCoordinate(x, y)) {
	                continue;
	            }
	            labels.push({ reference, x, y, offset: string.offset });
	        }
	        return labels;
	    }
	    function hierarchySheetReferenceForDisplayLabel(catalog, reference, existingReference) {
	        const referenceKey = normalizeSheetReferenceToken(reference);
	        const existingContentsKey = normalizeSheetReferenceToken(existingReference.contentsViewName);
	        return catalog.references.find((candidate) => {
	            const candidateReferenceKey = normalizeSheetReferenceToken(candidate.reference);
	            const candidateContentsKey = normalizeSheetReferenceToken(candidate.contentsViewName);
	            return (candidateReferenceKey === referenceKey &&
	                (!existingContentsKey ||
	                    candidateContentsKey === existingContentsKey ||
	                    areSheetReferenceKeysCompatible(candidateContentsKey, existingContentsKey)));
	        });
	    }
	    function sheetReferenceFromRefinLabel(value) {
	        const match = /^([A-Za-z][A-Za-z0-9_.-]*\d)_REFIN$/i.exec(value);
	        return match?.[1];
	    }
	    function isPlausiblePageLabelCoordinate(x, y) {
	        return x >= -100_000 && x <= 100_000 && y >= -100_000 && y <= 100_000;
	    }
	    function nearestSheetPartInstForDisplayLabel(label, partInsts, partInstanceOccurrenceReferences, claimedPartInsts) {
	        let best;
	        for (const partInst of partInsts) {
	            if (claimedPartInsts.has(partInst)) {
	                continue;
	            }
	            const existingReference = partInstanceOccurrenceReferences.get(partInst);
	            if (!existingReference ||
	                !areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(label.reference).replace(/\d+$/, ''), normalizeSheetReferenceToken(existingReference.reference).replace(/\d+$/, ''))) {
	                continue;
	            }
	            const distance = sheetReferenceDisplayLabelDistance(label, partInst);
	            if (distance > 160) {
	                continue;
	            }
	            if (!best || distance < best.distance) {
	                best = { partInst, distance };
	            }
	        }
	        return best?.partInst;
	    }
	    function sheetReferenceDisplayLabelDistance(label, partInst) {
	        const anchorX = partInst.LocX - 55;
	        const anchorY = partInst.LocY + 40;
	        return Math.abs(label.x - anchorX) + Math.abs(label.y - anchorY);
	    }
	    function isPotentialPageReferenceName(name) {
	        return /^[A-Za-z][A-Za-z0-9_.-]*\d[A-Za-z0-9_.-]*$/.test(name);
	    }
	    function isPageReferencePlaceholder(name) {
	        return name.endsWith('?') || /^(?:\?|UNKNOWN|UNANNOTATED|NONE|N\/A)$/i.test(name);
	    }
	    function precedingPageGraphicDbId(strings, index) {
	        for (let candidateIndex = index - 1; candidateIndex >= Math.max(0, index - 4); candidateIndex--) {
	            const candidate = strings[candidateIndex];
	            if (!/\.(?:Normal|IEEE|Convert)$/i.test(candidate.value)) {
	                continue;
	            }
	            const dbId = candidate.followingUInt32Le;
	            if (dbId !== undefined && dbId > 0 && dbId <= 1_000_000_000) {
	                return dbId;
	            }
	        }
	        return undefined;
	    }
	    function isBusEntryMemberName(name, explicitBusAliasNames = []) {
	        if (isExplicitBusAliasName(name)) {
	            return false;
	        }
	        if (name.includes('.')) {
	            return true;
	        }
	        if (!/\d(?:#)?$/i.test(name)) {
	            return false;
	        }
	        return (explicitBusAliasNames.some((busAliasName) => isExplicitBusAliasMemberName(name, busAliasName)) ||
	            isShortImplicitBusMemberName(name));
	    }
	    function isExplicitBusAliasMemberName(memberName, busAliasName) {
	        const prefix = busAliasName.replace(/\[[^\]]+\]/g, '');
	        const ordinal = /(\d+)(?:#)?$/i.exec(memberName)?.[1];
	        return Boolean(ordinal &&
	            prefix &&
	            (memberName === `${prefix}${ordinal}` || (!prefix.endsWith('_') && memberName === `${prefix}_${ordinal}`)));
	    }
	    function isShortImplicitBusMemberName(name) {
	        return name.split('_').length <= 3;
	    }
	    function busEntryBusPointCandidates(x, y) {
	        return [
	            { x: x - 10, y: y - 10 },
	            { x: x + 10, y: y - 10 },
	            { x: x - 10, y: y + 10 },
	            { x: x + 10, y: y + 10 },
	        ];
	    }
	    let PartPropertyPairBlockState;
	    (function (PartPropertyPairBlockState) {
	        PartPropertyPairBlockState["Absent"] = "absent";
	        PartPropertyPairBlockState["Unresolved"] = "unresolved";
	        PartPropertyPairBlockState["Resolved"] = "resolved";
	    })(PartPropertyPairBlockState || (PartPropertyPairBlockState = {}));
	    function partPropertyPairBlockState(object) {
	        if (object.propertyPairs === undefined) {
	            return PartPropertyPairBlockState.Absent;
	        }
	        return (object.resolvedPropertyPairCount ?? 0) > 0 ?
	            PartPropertyPairBlockState.Resolved
	            : PartPropertyPairBlockState.Unresolved;
	    }
	    function emitPartPropertySourceDiagnostic(diagnostics, dbId, state, countsBySource) {
	        const supplied = Array.from(countsBySource)
	            .filter(([, count]) => count > 0)
	            .map(([source, count]) => `${source}=${count}`);
	        emitBudgetedDiagnostic(diagnostics, `part-properties\0${dbId}`, state === PartPropertyPairBlockState.Absent ?
	            'part-properties-without-pair-block'
	            : 'part-properties-pair-block-unresolved', `dbId=${dbId} sources=${supplied.length > 0 ? supplied.join(',') : 'none'}`, 'CFB part properties', 'warning');
	    }
	    function reportNonReferencePlaceholder(properties, dbId, diagnostics) {
	        const placeholders = (properties ?? []).filter((property) => property.displayedValue !== undefined && /^[A-Za-z][A-Za-z0-9_.-]*\?$/.test(property.displayedValue));
	        const referenceRecord = placeholders.find((property) => property.propertyName === 'Part Reference');
	        if (referenceRecord) {
	            return;
	        }
	        const shown = placeholders[0];
	        if (!shown) {
	            return;
	        }
	        emitBudgetedDiagnostic(diagnostics, `part-reference\0${dbId}`, 'reference-placeholder-on-non-reference-record', `dbId=${dbId} record=${shown.propertyName ?? `0x${shown.propertyCode.toString(16)}`} value="${shown.displayedValue ?? ''}"`, 'CFB part reference', 'warning');
	    }
	    function buildPartInst(object, libraryInstanceCatalog, libraryStringCatalog, cisPageProperties, placedPartCacheIdentityCatalog, markerSourceLibraries, physicalPadPresenceCatalog, sheetSymbolPackageCatalog, hierarchySheetReferenceCatalog, partInstanceOccurrenceReferences, multipartSectionSuffixes, pageReferenceLookup, variantPropertyCatalog, physicalPinDiagnostics, hasRepeatedCatalogInstanceName, viewName) {
	        const metadata = object.resolvedMetadata;
	        const section = multipartSectionSuffixes.get(object);
	        const cacheIdentity = placedPartCacheIdentityCatalog.get(object);
	        const catalogEntry = cacheIdentity?.catalogEntry;
	        const displayProperties = (object.displayProperties ?? []).map(partDisplayPropertyFromCfb);
	        const pkgName = cacheIdentity?.packageName ?? metadata?.pkgName ?? object.graphicName.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
	        const libraryEntry = libraryEntryForPlacedPackage(libraryInstanceCatalog, object.dbId, pkgName);
	        const nearestLibraryEntry = libraryEntryForDbId(libraryInstanceCatalog, object.dbId);
	        const exactRowProperties = userPropertiesFromCfb((object.userProperties ?? []).filter((property) => property.source === 'library-catalog'));
	        const variantProperties = variantUserPropertiesForPart(variantPropertyCatalog, exactRowProperties, object, pkgName);
	        const cisProperties = cisPageProperties?.get(object.dbId) ?? [];
	        const displayDerivedProperties = userPropertiesFromCfb(partUserPropertiesFromCfbObject(object));
	        const userProperties = finalizePartUserProperties(mergeUserProperties(exactRowProperties, cisProperties, variantProperties, displayDerivedProperties)).filter((property) => object.valueState !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld || property.Name !== 'Value');
	        const pairBlockState = partPropertyPairBlockState(object);
	        if (pairBlockState !== PartPropertyPairBlockState.Resolved) {
	            emitPartPropertySourceDiagnostic(physicalPinDiagnostics, object.dbId, pairBlockState, new Map([
	                ['cis-schematic-info', cisProperties.length],
	                ['variant-update', variantProperties.length],
	                [
	                    'display-property',
	                    partUserPropertiesFromCfbObject(object).filter((property) => property.source === 'display-property').length,
	                ],
	                ['display-catalog-value', exactRowProperties.length],
	            ]));
	        }
	        const dbIdSheetReference = hierarchySheetReferenceForDbId(hierarchySheetReferenceCatalog, object.dbId, viewName);
	        const exactSheetPackage = findSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, pkgName);
	        const strictSheetPackage = exactSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, pkgName);
	        const displaySheetPackage = sheetPackageFromDisplayCatalog(object, libraryStringCatalog, exactSheetPackage, physicalPinDiagnostics);
	        const exactSheetPackageWithDisplayValue = exactSheetPackage && displaySheetPackage ?
	            mergeSheetSymbolPackageMetadata(exactSheetPackage, displaySheetPackage)
	            : exactSheetPackage;
	        const candidateSheetPackage = (shouldPreferDisplaySheetPackage(displaySheetPackage, exactSheetPackage) ? displaySheetPackage : undefined) ??
	            exactSheetPackageWithDisplayValue ??
	            findSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, dbIdSheetReference?.contentsViewName) ??
	            findSheetSymbolPackageMetadata(sheetSymbolPackageCatalog, object.partValue) ??
	            displaySheetPackage;
	        const hasHierarchyOccurrenceAnnotation = hierarchySheetReferenceCatalog.references.some((reference) => reference.isOccurrenceAnnotation === true &&
	            (reference.parentViewName === undefined || reference.parentViewName.toUpperCase() === viewName.toUpperCase()) &&
	            reference.dbId === object.dbId &&
	            !hierarchySheetReferenceCatalog.usedIndexes.has(hierarchySheetReferenceCatalog.references.indexOf(reference)));
	        const sheetReference = consumeHierarchySheetReference(hierarchySheetReferenceCatalog, candidateSheetPackage, pkgName, object.dbId, object, physicalPinDiagnostics, Boolean(strictSheetPackage ||
	            (object.materializedPortInstances?.length ?? 0) >= 5 ||
	            (dbIdSheetReference &&
	                candidateSheetPackage &&
	                areSheetReferenceKeysCompatible(dbIdSheetReference.contentsViewName, candidateSheetPackage.contentsViewName))), viewName);
	        const isHierarchyCarrier = Boolean(sheetReference || hasHierarchyOccurrenceAnnotation);
	        const sheetPackage = isHierarchyCarrier ? candidateSheetPackage : undefined;
	        const ports = cacheIdentity === null ?
	            { scalar: [], bus: [] }
	            : portInstsFromMaterializedPorts(materializedPartPorts(object, cacheIdentity?.cachePins, isHierarchyCarrier), object.dbId, physicalPadPresenceCatalog, [object.graphicName, pkgName, canonicalSourceLibraryName(cacheIdentity?.cacheSourceLibName)], physicalPinDiagnostics, isHierarchyCarrier || cacheIdentity?.hasDeclaredBounds === true);
	        const libName = cacheIdentity === null ? '' : ((cacheIdentity?.sourceLibName ?? markerSourceLibraries.get(object) ?? metadata?.libName ?? ''));
	        const objectPartValue = usablePlacedPartValue(object.partValue, cacheIdentity?.cachePins) ? object.partValue : undefined;
	        const libraryPartValue = (usablePlacedPartValue(userProperties.find((property) => property.Name === 'Value')?.Value, cacheIdentity?.cachePins)) ?
	            userProperties.find((property) => property.Name === 'Value')?.Value
	            : undefined;
	        const catalogPartValue = usablePlacedPartValue(catalogEntry?.partValue, cacheIdentity?.cachePins) ? catalogEntry?.partValue : undefined;
	        const libraryPcbFootprint = userProperties.find((property) => property.Name === 'PCB Footprint')?.Value;
	        const pageReference = pageReferenceLookup.get(object.dbId);
	        reportNonReferencePlaceholder(object.displayProperties, object.dbId, physicalPinDiagnostics);
	        const genericSheetReferencePrefix = sheetPackage && isGenericSheetViewName(sheetPackage.contentsViewName) ? sheetPackage.contentsViewName : undefined;
	        const objectReference = pageReference ??
	            (sheetPackage && (isSheetMetadataValue(object.reference, sheetPackage) || genericSheetReferencePrefix) ?
	                undefined
	                : object.reference);
	        const { reference: recordedReference, unannotated } = recordedPlacementReference(object, [
	            objectReference,
	            sheetPackage?.referencePrefix ? `${sheetPackage.referencePrefix}?` : undefined,
	        ]);
	        const sheetContentsViewName = dbIdSheetReference?.contentsViewName ??
	            (sheetPackage?.contentsViewName && !isGenericSheetViewName(sheetPackage.contentsViewName) ?
	                sheetPackage.contentsViewName
	                : (sheetReference?.contentsViewName ?? sheetPackage?.contentsViewName));
	        const catalogSectionInstance = isCatalogSectionInstance(object, pkgName, catalogEntry);
	        const graphicSectionDesignator = deviceDesignatorFromGraphicSection(object.graphicName, pkgName);
	        const partValue = object.valueState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld ? ''
	            : sheetPackage ?
	                (metadata?.partValue ??
	                    sheetPackage.partValue ??
	                    objectPartValue ??
	                    libraryPartValue ??
	                    catalogPartValue ??
	                    pkgName)
	                : catalogSectionInstance ? catalogSectionPartValue(libraryPartValue, pkgName)
	                    : (metadata?.partValue ?? objectPartValue ?? libraryPartValue ?? catalogPartValue ?? pkgName);
	        const partInst = {
	            Color: object.color,
	            DbId: object.dbId,
	            DeviceDesignator: metadata?.deviceDesignator ??
	                (section?.state === MultipartSectionState.Withheld ? '' : section?.designator) ??
	                catalogEntry?.deviceDesignator ??
	                graphicSectionDesignator ??
	                '',
	            LibName: libName,
	            LocX: object.locX,
	            LocY: object.locY,
	            Mirror: metadata?.mirror ?? 0,
	            Name: (hasRepeatedCatalogInstanceName &&
	                nearestLibraryEntry &&
	                object.dbId - nearestLibraryEntry.instanceNumber <= 32) ?
	                nearestLibraryEntry.record.name
	                : (libraryEntry?.record.name ?? metadata?.name ?? `INS${object.dbId}`),
	            PkgName: pkgName,
	            PowerPinsVisible: metadata?.powerPinsVisible ?? false,
	            Rotation: metadata?.rotation ?? 0,
	            GraphicName: object.graphicName,
	            ...(displayProperties.length > 0 ? { DisplayProperty: displayProperties } : {}),
	            ...(userProperties.length > 0 ? { UserProperty: userProperties } : {}),
	            ContentsLibName: nonEmpty(metadata?.contentsLibName) ?? sheetPackage?.contentsLibName ?? '',
	            ContentsViewName: nonEmpty(metadata?.contentsViewName) ?? sheetContentsViewName ?? catalogEntry?.contentsViewName ?? '',
	            ContentsViewType: isHierarchyCarrier ? 1 : (metadata?.contentsViewType ?? 5),
	            IsPrimitiveProp: isHierarchyCarrier ? 0 : (metadata?.isPrimitiveProp ?? 2),
	            PartValue: partValue,
	            Reference: (object.referenceState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld ||
	                (unannotated && section?.state === MultipartSectionState.ReferenceWithheld)) ?
	                ''
	                : recordedReference,
	            ...(ports.scalar.length > 0 && section?.state !== MultipartSectionState.Withheld ?
	                { PortInstScalar: ports.scalar }
	                : {}),
	            ...(ports.bus.length > 0 && section?.state !== MultipartSectionState.Withheld ? { PortInstBus: ports.bus } : {}),
	            ...(metadata?.pcbLib || libraryPcbFootprint ? { PCBLib: metadata?.pcbLib ?? libraryPcbFootprint } : {}),
	        };
	        if (sheetReference?.reference) {
	            partInstanceOccurrenceReferences.set(partInst, sheetReference);
	        }
	        return partInst;
	    }
	    function isCatalogSectionInstance(object, pkgName, catalogEntry) {
	        const graphicPackageName = stripNormalViewSuffix(object.graphicName);
	        return Boolean((catalogEntry || graphicPackageName !== pkgName) &&
	            graphicPackageName !== pkgName &&
	            isSectionOfCatalogPackage(graphicPackageName, pkgName));
	    }
	    function catalogSectionPartValue(libraryPartValue, pkgName) {
	        if (libraryPartValue &&
	            (libraryPartValue === pkgName ||
	                (/[a-z]/.test(libraryPartValue) && !/[A-Z]\d+_/.test(libraryPartValue)) ||
	                (/^[A-Z]+\d[A-Z0-9]*$/.test(libraryPartValue) && !libraryPartValue.includes('_')) ||
	                (/^(?=.*[A-Z])(?=.*\d)[A-Z0-9]{5,}$/.test(libraryPartValue) && !libraryPartValue.includes('_')))) {
	            return libraryPartValue;
	        }
	        return pkgName;
	    }
	    function deviceDesignatorFromGraphicSection(graphicName, pkgName) {
	        const graphicPackageName = stripNormalViewSuffix(graphicName);
	        if (!graphicPackageName.startsWith(pkgName)) {
	            return undefined;
	        }
	        const suffix = graphicPackageName.slice(pkgName.length).replace(/^[-_]/, '');
	        return suffix && isDeviceDesignatorToken(suffix) ? suffix : undefined;
	    }
	    function variantUserPropertiesForPart(catalog, displayCatalogProperties, object, pkgName) {
	        const byName = new Map(displayCatalogProperties.map((property) => [property.Name, property.Value]));
	        const partNumber = byName.get('Part_Number');
	        const value = byName.get('Value') ?? object.partValue;
	        if (!partNumber || !value) {
	            return [];
	        }
	        const properties = catalog.get(variantPropertyKey(partNumber, value)) ?? [];
	        const sourcePackage = properties.find((property) => property.Name === 'Source Package')?.Value;
	        return !sourcePackage || sourcePackage === pkgName ? properties : [];
	    }
	    function materializedPartPorts(object, cachePins, isSheetSymbol = false) {
	        const ports = applyCachePinNamesToMaterializedPorts(object.materializedPortInstances ?? [], cachePins);
	        applyRecordedPortCalibrationToMissingCachePins(object, ports);
	        return orderMaterializedPartPortsForXml(object, ports, isSheetSymbol);
	    }
	    function isPlacedPinAnchor(port) {
	        return port.x !== undefined && port.y !== undefined && port.hotptX !== undefined && port.hotptY !== undefined;
	    }
	    function applyRecordedPortCalibrationToMissingCachePins(object, ports) {
	        const anchors = ports.filter(isPlacedPinAnchor);
	        if (anchors.length === 0) {
	            return;
	        }
	        for (const port of ports) {
	            if (port.x !== undefined ||
	                port.y !== undefined ||
	                port.source !== 'symbol-pin' ||
	                port.isExplicit !== false ||
	                port.hotptX === undefined ||
	                port.hotptY === undefined) {
	                continue;
	            }
	            const predictedPoints = new Map();
	            for (const anchor of anchors) {
	                const delta = transformPlacedPinDelta(object, port.hotptX - anchor.hotptX, port.hotptY - anchor.hotptY);
	                const point = { x: anchor.x + delta.x, y: anchor.y + delta.y };
	                predictedPoints.set(pagePointKey(point.x, point.y), point);
	            }
	            if (predictedPoints.size === 1) {
	                const point = predictedPoints.values().next().value;
	                if (point) {
	                    port.x = point.x;
	                    port.y = point.y;
	                }
	            }
	        }
	    }
	    function transformPlacedPinDelta(object, x, y) {
	        const mirror = object.resolvedMetadata?.mirror ?? ((object.flags & 0x04) !== 0 ? 1 : 0);
	        const mirroredX = mirror === 1 ? -x : x;
	        const rotation = object.resolvedMetadata?.rotation ?? object.flags & 0x03;
	        return (rotation === 1 ? { x: y, y: -mirroredX }
	            : rotation === 2 ? { x: -mirroredX, y: -y }
	                : rotation === 3 ? { x: -y, y: mirroredX }
	                    : { x: mirroredX, y });
	    }
	    function orderMaterializedPartPortsForXml(object, ports, isSheetSymbol) {
	        if (isSheetSymbol ||
	            object.resolvedMetadata?.contentsViewType === 1 ||
	            isDsnSourcePath(object.resolvedMetadata?.contentsLibName ?? '')) {
	            return [...ports].sort((left, right) => compareXmlTextOrder(left.name, right.name) || (left.position ?? 0) - (right.position ?? 0));
	        }
	        return ports;
	    }
	    function applyCachePinNamesToMaterializedPorts(ports, cachePins) {
	        if (!cachePins?.length) {
	            return [...ports];
	        }
	        const usedCachePositions = new Set();
	        const mapped = ports.map((port, index) => {
	            const cachePin = matchingCachePinForMaterializedPort(port, cachePins, index, usedCachePositions);
	            return cachePin ?
	                {
	                    ...port,
	                    position: cachePins.indexOf(cachePin),
	                    name: cachePin.name,
	                    type: cachePin.pinType ?? port.type,
	                    hotptX: port.hotptX ?? cachePin.hotptX,
	                    hotptY: port.hotptY ?? cachePin.hotptY,
	                    startX: port.startX ?? cachePin.startX,
	                    startY: port.startY ?? cachePin.startY,
	                    isNoConnect: port.isNoConnect,
	                }
	                : { ...port };
	        });
	        const usedPositions = new Set(mapped.map((port, index) => port.position ?? index));
	        for (let position = 0; position < cachePins.length; position++) {
	            if (usedPositions.has(position)) {
	                continue;
	            }
	            mapped.push(materializedPortFromCachePin(cachePins[position], position));
	        }
	        return mapped.sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
	    }
	    function materializedPortFromCachePin(pin, position) {
	        return {
	            name: pin.name,
	            position,
	            pinIndex: position + 1,
	            startX: pin.startX,
	            startY: pin.startY,
	            hotptX: pin.hotptX,
	            hotptY: pin.hotptY,
	            isNoConnect: false,
	            isBus: false,
	            type: pin.pinType ?? DEFAULT_PIN_TYPE,
	            source: 'symbol-pin',
	            isExplicit: false,
	        };
	    }
	    function matchingCachePinForMaterializedPort(port, cachePins, index, usedCachePositions) {
	        if (port.source === 'record' &&
	            port.isExplicit === true &&
	            port.position !== undefined &&
	            port.pinIndex === port.position + 1) {
	            if (usedCachePositions.has(port.position)) {
	                return undefined;
	            }
	            const recorded = cachePins[port.position];
	            if (recorded) {
	                usedCachePositions.add(port.position);
	            }
	            return recorded;
	        }
	        const hotptX = port.hotptX ?? port.x ?? 0;
	        const hotptY = port.hotptY ?? port.y ?? 0;
	        const startX = port.startX ?? hotptX;
	        const startY = port.startY ?? hotptY;
	        const exact = cachePins.find((pin, position) => !usedCachePositions.has(position) &&
	            pin.hotptX === hotptX &&
	            pin.hotptY === hotptY &&
	            pin.startX === startX &&
	            pin.startY === startY);
	        if (exact) {
	            usedCachePositions.add(cachePins.indexOf(exact));
	            return exact;
	        }
	        const fallbackPosition = port.position ?? index;
	        if (usedCachePositions.has(fallbackPosition)) {
	            return undefined;
	        }
	        const fallback = cachePins[fallbackPosition];
	        if (fallback) {
	            usedCachePositions.add(fallbackPosition);
	        }
	        return fallback;
	    }
	    function buildDrawnInst(object, libraryStringCatalog, diagnostics, portDbIds) {
	        const ports = drawnPortInstsFromMaterializedPorts(object.materializedPortInstances ?? [], portDbIds);
	        const displayProperties = (object.displayProperties ?? []).map(drawnInstDisplayPropertyFromCfb);
	        const bounds = drawnInstBoundsFromPorts(object);
	        return {
	            Color: object.color,
	            DbId: object.dbId,
	            DeviceDesignator: object.deviceDesignator ?? object.reference ?? '',
	            LibName: '',
	            LocX: object.locX,
	            LocY: object.locY,
	            Mirror: object.mirror,
	            Name: drawnInstName(object, diagnostics),
	            Rotation: object.rotation,
	            X1: bounds.X1,
	            X2: bounds.X2,
	            Y1: bounds.Y1,
	            Y2: bounds.Y2,
	            ...(displayProperties.length > 0 ? { DisplayProperty: displayProperties } : {}),
	            ContentsLibName: drawnInstContentsLibName(object, libraryStringCatalog),
	            ContentsViewName: object.contentsViewName ?? '',
	            ContentsViewType: object.contentsViewType ?? 1,
	            IsPrimitiveProp: object.isPrimitiveProp ?? 2,
	            PartValue: object.partValue ?? object.contentsViewName ?? object.name ?? '',
	            Reference: object.reference ?? object.deviceDesignator ?? '',
	            ...(ports.scalar.length > 0 ? { PortInstScalar: ports.scalar } : {}),
	            ...(ports.bus.length > 0 ? { PortInstBus: ports.bus } : {}),
	        };
	    }
	    function drawnInstContentsLibName(object, libraryStringCatalog) {
	        const decoded = object.contentsLibName;
	        if (decoded !== undefined && decoded.length > 0) {
	            return decoded;
	        }
	        return externalSchematicPathForView(libraryStringCatalog, object.contentsViewName) ?? '';
	    }
	    function drawnInstName(object, diagnostics) {
	        const name = object.name ?? object.deviceDesignator;
	        if (name === undefined) {
	            emitBudgetedDiagnostic(diagnostics, `${object.dbId}`, 'sheet-symbol-name-undecoded', `dbId=${object.dbId} contentsViewName="${object.contentsViewName ?? ''}"`, 'CFB sheet symbol', 'warning');
	            return '';
	        }
	        if (!name.includes(' ') || name !== name.toUpperCase() || /\d/.test(name)) {
	            return name;
	        }
	        return name.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
	    }
	    function externalSchematicPathForView(libraryStringCatalog, contentsViewName) {
	        if (!contentsViewName) {
	            return undefined;
	        }
	        const expected = `${contentsViewName}.DSN`;
	        const candidates = libraryStringCatalog
	            .map((entry) => entry.value)
	            .filter((value) => value.toUpperCase().endsWith(expected.toUpperCase()))
	            .sort((left, right) => externalSchematicPathScore(right, contentsViewName) - externalSchematicPathScore(left, contentsViewName));
	        return candidates[0];
	    }
	    function externalSchematicPathScore(value, contentsViewName) {
	        let score = 0;
	        if (value.startsWith('.\\')) {
	            score += 100;
	        }
	        if (value.includes(`\\${contentsViewName}\\`)) {
	            score += 20;
	        }
	        if (value === `.\\${contentsViewName}\\${contentsViewName}.DSN`) {
	            score += 10;
	        }
	        score -= value.length / 100;
	        return score;
	    }
	    function isSheetMetadataValue(value, sheetPackage) {
	        const key = normalizeSheetReferenceToken(value);
	        return Boolean(key &&
	            (areSheetReferenceKeysCompatible(key, normalizeSheetReferenceToken(sheetPackage.contentsViewName)) ||
	                areSheetReferenceKeysCompatible(key, normalizeSheetReferenceToken(sheetPackage.partValue))));
	    }
	    function sheetPackageFromDisplayCatalog(object, libraryStrings, exactSheetPackage, diagnostics) {
	        const indexes = [
	            object.referenceTail?.valueStringIndex,
	            ...(object.displayProperties ?? []).map((property) => property.libraryStringIndex),
	        ].filter((index) => index !== undefined && index > 0 && index < libraryStrings.length);
	        for (const index of indexes) {
	            const metadata = sheetPackageFromLibraryStringIndex(libraryStrings, index, object, exactSheetPackage, diagnostics);
	            if (metadata) {
	                return metadata;
	            }
	        }
	        return undefined;
	    }
	    function sheetPackageFromLibraryStringIndex(libraryStrings, index, object, exactSheetPackage, diagnostics) {
	        const indexedValue = libraryStrings[index]?.value;
	        const indexRecord = libraryRecordBounds(libraryStrings, index);
	        if (indexRecord.truncated) {
	            return undefined;
	        }
	        const packageName = stripNormalViewSuffix(object.graphicName);
	        const owned = ownedLibraryDsnPath(libraryStrings, index, indexRecord, packageName, exactSheetPackage, diagnostics);
	        if (!owned) {
	            return undefined;
	        }
	        const { dsnIndex, contentsLibName, pathViewName, ownedViewName } = owned;
	        const pathRecord = libraryRecordBounds(libraryStrings, dsnIndex);
	        const { partValue } = librarySheetPartValueInRecord(libraryStrings, dsnIndex, pathRecord);
	        if (exactSheetPackage?.contentsLibName &&
	            isDsnSourcePath(exactSheetPackage.contentsLibName) &&
	            childDsnPathMatchesView(exactSheetPackage.contentsLibName, exactSheetPackage.contentsViewName) &&
	            !isSameDsnFileName(exactSheetPackage.contentsLibName, contentsLibName)) {
	            return undefined;
	        }
	        const exactSheetViewName = exactSheetPackage?.contentsViewName && !isGenericSheetViewName(exactSheetPackage.contentsViewName) ?
	            exactSheetPackage.contentsViewName
	            : undefined;
	        const contentsViewName = ownedViewName ?? exactSheetViewName ?? exactSheetPackage?.contentsViewName ?? pathViewName;
	        const indexedSheetViewName = indexedValue && isDisplaySheetViewAliasForPath(indexedValue, pathViewName) ? indexedValue : undefined;
	        return {
	            packageName,
	            contentsLibName,
	            contentsViewName: indexedSheetViewName ?? contentsViewName,
	            ...(exactSheetPackage?.referencePrefix ? { referencePrefix: exactSheetPackage.referencePrefix } : {}),
	            ...(partValue ? { partValue } : {}),
	        };
	    }
	    function ownedLibraryDsnPath(libraryStrings, index, indexRecord, packageName, exactSheetPackage, diagnostics) {
	        const indexedValue = libraryStrings[index]?.value;
	        const owners = [];
	        const windowStart = Math.max(0, index - LIBRARY_DISPLAY_PATH_WINDOW);
	        const windowEnd = Math.min(libraryStrings.length, index + LIBRARY_DISPLAY_PATH_WINDOW + 1);
	        for (let candidate = windowStart; candidate < windowEnd; candidate++) {
	            const rawPath = libraryStrings[candidate]?.value;
	            if (!isDsnSourcePath(rawPath)) {
	                continue;
	            }
	            const contentsLibName = preferredProjectModuleDsnPath(libraryStrings, rawPath);
	            const pathViewName = contentsLibName ? dsnViewNameFromPath(contentsLibName) : undefined;
	            if (!contentsLibName || !pathViewName) {
	                continue;
	            }
	            const namedByExactPath = exactSheetPackage?.contentsLibName !== undefined &&
	                isDsnSourcePath(exactSheetPackage.contentsLibName) &&
	                isSameDsnFileName(exactSheetPackage.contentsLibName, contentsLibName);
	            const namedByIndexedValue = Boolean(indexedValue && isDisplaySheetViewAliasForPath(indexedValue, pathViewName));
	            const namedByPackageName = isSameChildDesign(packageName, pathViewName);
	            const namedByExactView = exactSheetPackage?.contentsViewName !== undefined &&
	                isSameChildDesign(exactSheetPackage.contentsViewName, pathViewName);
	            const namedByPackage = namedByPackageName ? true : namedByExactView;
	            const recordViewName = libraryStrings
	                .slice(indexRecord.start, indexRecord.end)
	                .map((row) => row.value)
	                .find((value) => value !== rawPath && isDisplaySheetViewAliasForPath(value, pathViewName));
	            const rank = namedByExactPath ? 0
	                : namedByIndexedValue ? 1
	                    : namedByPackage ? 2
	                        : recordViewName ? 3
	                            : undefined;
	            if (rank === undefined) {
	                continue;
	            }
	            const ownedViewName = namedByIndexedValue ? indexedValue : recordViewName;
	            owners.push({
	                dsnIndex: candidate,
	                contentsLibName,
	                pathViewName,
	                ...(ownedViewName ? { ownedViewName } : {}),
	                rank,
	                distance: Math.abs(candidate - index),
	            });
	        }
	        if (owners.length === 0) {
	            return undefined;
	        }
	        owners.sort((left, right) => left.rank - right.rank || left.distance - right.distance);
	        const best = owners[0];
	        const rival = owners.find((owner) => owner.rank === best.rank &&
	            owner.distance === best.distance &&
	            !isSameChildDesign(owner.contentsLibName, best.contentsLibName));
	        if (!rival) {
	            return best;
	        }
	        emitBudgetedDiagnostic(diagnostics, `${packageName}\0${index}`, 'sheet-owner-ambiguous', `the display row names "${best.pathViewName}" and "${rival.pathViewName}" on the same evidence and at the same distance, so it names neither`, 'Sheet symbol owner ambiguous', 'warning');
	        return undefined;
	    }
	    const LIBRARY_DISPLAY_PATH_WINDOW = 8;
	    function preferredProjectModuleDsnPath(libraryStrings, rawPath, diagnostics) {
	        if (!rawPath) {
	            return undefined;
	        }
	        const basename = pathBasename(rawPath).toLowerCase();
	        const projectLocalPaths = new Map();
	        for (const entry of libraryStrings) {
	            const path = entry.value.replace(/\//g, '\\');
	            if (!/^\.\\modules\\/i.test(path) || pathBasename(path).toLowerCase() !== basename) {
	                continue;
	            }
	            const key = collapsedWindowsPath(path).toLowerCase();
	            if (!projectLocalPaths.has(key)) {
	                projectLocalPaths.set(key, path);
	            }
	        }
	        const [projectLocalPath] = projectLocalPaths.values();
	        if (projectLocalPaths.size === 1 && projectLocalPath !== undefined) {
	            return canonicalChildDsnPath(projectLocalPath);
	        }
	        if (diagnostics) {
	            emitBudgetedDiagnostic(diagnostics, basename, projectLocalPaths.size === 0 ? 'child-path-unresolved' : 'child-path-ambiguous', `recorded="${rawPath}" registered=${projectLocalPaths.size}`, 'CFB hierarchy', 'warning');
	        }
	        return canonicalChildDsnPath(rawPath);
	    }
	    function collapsedWindowsPath(path) {
	        const resolved = [];
	        for (const segment of path.split('\\')) {
	            if (segment === '.') {
	                continue;
	            }
	            if (segment === '..') {
	                const previous = resolved[resolved.length - 1];
	                if (previous === undefined || previous === '..') {
	                    resolved.push(segment);
	                    continue;
	                }
	                resolved.pop();
	                continue;
	            }
	            const trimmed = segment.replace(/[. ]+$/, '');
	            if (trimmed === '' && segment !== '') {
	                continue;
	            }
	            resolved.push(trimmed);
	        }
	        return resolved.join('\\');
	    }
	    function shouldPreferDisplaySheetPackage(displaySheetPackage, exactSheetPackage) {
	        if (!displaySheetPackage || !exactSheetPackage) {
	            return Boolean(displaySheetPackage);
	        }
	        if (isGenericSheetViewName(exactSheetPackage.contentsViewName)) {
	            return true;
	        }
	        if (displaySheetPackage.contentsViewName === exactSheetPackage.contentsViewName) {
	            return false;
	        }
	        if (areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(displaySheetPackage.contentsViewName), normalizeSheetReferenceToken(exactSheetPackage.contentsViewName))) {
	            return false;
	        }
	        return /\d/.test(displaySheetPackage.contentsViewName) && /\d/.test(exactSheetPackage.contentsViewName);
	    }
	    const UNNAMED_CONTENTS_VIEW = '';
	    function isGenericSheetViewName(value) {
	        return (value === UNNAMED_CONTENTS_VIEW ||
	            (value !== undefined && value.toUpperCase() === OrcadDsnCfbMaterializer_1.PENDING_CONTENTS_VIEW_NAME.toUpperCase()));
	    }
	    function isDisplaySheetViewAliasForPath(value, pathViewName) {
	        return areSheetReferenceKeysCompatible(normalizeSheetReferenceToken(value), normalizeSheetReferenceToken(pathViewName));
	    }
	    function hierarchySheetReferenceForDbId(catalog, objectDbId, viewName) {
	        if (objectDbId === undefined) {
	            return undefined;
	        }
	        return catalog.references.find((reference, index) => !catalog.usedIndexes.has(index) &&
	            reference.dbId === objectDbId &&
	            (reference.parentViewName === undefined || reference.parentViewName.toUpperCase() === viewName.toUpperCase()));
	    }
	    function consumeHierarchySheetReference(catalog, sheetPackage, pkgName, objectDbId, object, diagnostics, allowPackageFallback = true, viewName) {
	        if (objectDbId !== undefined) {
	            const annotationIndexes = [];
	            for (let index = 0; index < catalog.references.length; index++) {
	                if (catalog.usedIndexes.has(index)) {
	                    continue;
	                }
	                const reference = catalog.references[index];
	                if (reference.dbId !== objectDbId ||
	                    (reference.parentViewName !== undefined && reference.parentViewName.toUpperCase() !== viewName?.toUpperCase())) {
	                    continue;
	                }
	                if (!reference.isOccurrenceAnnotation &&
	                    sheetPackage &&
	                    (allowPackageFallback ||
	                        areSheetReferenceKeysCompatible(reference.contentsViewName, sheetPackage.contentsViewName))) {
	                    const claimKey = hierarchySheetClaimKey(sheetPackage, pkgName);
	                    return claimHierarchySheetReference(catalog, index, claimKey);
	                }
	                if (reference.isOccurrenceAnnotation) {
	                    annotationIndexes.push(index);
	                }
	            }
	            if (annotationIndexes.length > 0) {
	                const views = new Set(annotationIndexes.map((index) => catalog.references[index].contentsViewName.toUpperCase()));
	                const annotation = catalog.references[annotationIndexes[0]];
	                const namedPorts = new Set((object?.materializedPortInstances ?? [])
	                    .map((port) => port.name.trim().toUpperCase())
	                    .filter((name) => name.length > 0));
	                const hasExplicitTarget = Boolean(sheetPackage &&
	                    isDsnSourcePath(sheetPackage.contentsLibName) &&
	                    areSheetReferenceKeysCompatible(sheetPackage.contentsViewName, annotation.contentsViewName));
	                if (views.size === 1 && hasExplicitTarget && namedPorts.size > 0 && sheetPackage) {
	                    return claimHierarchySheetReference(catalog, annotationIndexes[0], hierarchySheetClaimKey(sheetPackage, pkgName));
	                }
	                if (diagnostics) {
	                    emitBudgetedDiagnostic(diagnostics, String(objectDbId), views.size > 1 ? 'hierarchy-annotation-ambiguous' : 'hierarchy-annotation-unresolved', `dbId=${objectDbId} package="${pkgName}" views=${Array.from(views).sort().join(',')} target=${hasExplicitTarget ? 'present' : 'missing'} ports=${namedPorts.size}`, 'CFB hierarchy', 'warning');
	                }
	                return undefined;
	            }
	        }
	        if (!sheetPackage) {
	            return undefined;
	        }
	        if (!allowPackageFallback) {
	            return undefined;
	        }
	        const claimKey = hierarchySheetClaimKey(sheetPackage, pkgName);
	        const claimOrdinal = (catalog.claimCounts.get(claimKey) ?? 0) + 1;
	        if (isGenericSheetViewName(sheetPackage.contentsViewName)) {
	            const moduleKeys = [pkgName, sheetPackage.packageName, sheetPackage.partValue]
	                .map((name) => normalizedSheetModuleInstanceKey(name))
	                .filter((key) => key !== '');
	            const matchingIndexes = [];
	            for (let index = 0; index < catalog.references.length; index++) {
	                if (catalog.usedIndexes.has(index)) {
	                    continue;
	                }
	                const reference = catalog.references[index];
	                if (reference.isOccurrenceAnnotation ||
	                    (reference.parentViewName !== undefined && reference.parentViewName.toUpperCase() !== viewName?.toUpperCase())) {
	                    continue;
	                }
	                const referenceKey = normalizedSheetModuleInstanceKey(reference.reference);
	                if (moduleKeys.some((key) => areSheetReferenceKeysCompatible(key, referenceKey))) {
	                    matchingIndexes.push(index);
	                }
	            }
	            if (matchingIndexes.length === 0 && diagnostics) {
	                emitPhysicalPinDiagnostic(diagnostics, pkgName, 'sheet-reference-module-unregistered', `package="${pkgName}" modules="${moduleKeys.join(',')}"`);
	            }
	            const selectedIndex = selectHierarchySheetReferenceIndex(catalog, matchingIndexes, sheetPackage, pkgName, claimOrdinal);
	            if (selectedIndex !== undefined) {
	                return claimHierarchySheetReference(catalog, selectedIndex, claimKey);
	            }
	        }
	        const packageKey = normalizeSheetReferenceToken(sheetPackage.contentsViewName);
	        const matchingIndexes = [];
	        for (let index = 0; index < catalog.references.length; index++) {
	            if (catalog.usedIndexes.has(index)) {
	                continue;
	            }
	            const reference = catalog.references[index];
	            if (reference.isOccurrenceAnnotation ||
	                (reference.parentViewName !== undefined && reference.parentViewName.toUpperCase() !== viewName?.toUpperCase())) {
	                continue;
	            }
	            const referenceKey = normalizeSheetReferenceToken(reference.contentsViewName);
	            if (areSheetReferenceKeysCompatible(referenceKey, packageKey)) {
	                matchingIndexes.push(index);
	            }
	        }
	        const selectedIndex = selectHierarchySheetReferenceIndex(catalog, matchingIndexes, sheetPackage, pkgName, claimOrdinal);
	        return selectedIndex !== undefined ? claimHierarchySheetReference(catalog, selectedIndex, claimKey) : undefined;
	    }
	    function claimHierarchySheetReference(catalog, index, claimKey) {
	        catalog.usedIndexes.add(index);
	        catalog.claimCounts.set(claimKey, (catalog.claimCounts.get(claimKey) ?? 0) + 1);
	        return catalog.references[index];
	    }
	    function selectHierarchySheetReferenceIndex(catalog, indexes, sheetPackage, pkgName, claimOrdinal) {
	        if (indexes.length === 0) {
	            return undefined;
	        }
	        return (indexes.find((index) => hierarchyReferenceOrdinal(catalog.references[index].reference) === claimOrdinal &&
	            hierarchyReferenceMatchesSheetPackage(catalog.references[index], sheetPackage, pkgName)) ??
	            indexes.find((index) => hierarchyReferenceOrdinal(catalog.references[index].reference) === claimOrdinal) ??
	            indexes.find((index) => hierarchyReferenceMatchesSheetPackage(catalog.references[index], sheetPackage, pkgName)) ??
	            indexes[0]);
	    }
	    function hierarchySheetClaimKey(sheetPackage, pkgName) {
	        const candidates = [
	            pathBasename(sheetPackage.contentsLibName),
	            sheetPackage.packageName,
	            pkgName,
	            sheetPackage.contentsViewName,
	        ].map(normalizeSheetReferenceToken);
	        return candidates.find((candidate) => candidate.length > 0) ?? '';
	    }
	    function hierarchyReferenceOrdinal(reference) {
	        const match = /(\d+)$/.exec(reference);
	        return match ? Number.parseInt(match[1], 10) : undefined;
	    }
	    function hierarchyReferenceMatchesSheetPackage(reference, sheetPackage, pkgName) {
	        const referenceKey = normalizeSheetReferenceToken(reference.reference).replace(/\d+$/, '');
	        if (!referenceKey) {
	            return false;
	        }
	        const packageKeys = [
	            pathBasename(sheetPackage.contentsLibName),
	            sheetPackage.packageName,
	            pkgName,
	            sheetPackage.contentsViewName,
	        ]
	            .map((value) => normalizeSheetReferenceToken(value).replace(/\d+$/, ''))
	            .filter(Boolean);
	        return packageKeys.some((packageKey) => packageKey.includes(referenceKey) ||
	            referenceKey.includes(packageKey) ||
	            areSheetReferenceKeysCompatible(packageKey, referenceKey));
	    }
	    function findSheetSymbolPackageMetadata(catalog, pkgName) {
	        if (!pkgName) {
	            return undefined;
	        }
	        const exact = exactSheetSymbolPackageMetadata(catalog, pkgName);
	        if (exact) {
	            return exact;
	        }
	        const pkgKey = normalizeSheetReferenceToken(pkgName);
	        return Array.from(catalog.values()).find((candidate) => {
	            if (candidate.contentsViewName === UNNAMED_CONTENTS_VIEW) {
	                return false;
	            }
	            const packageKey = normalizeSheetReferenceToken(candidate.packageName);
	            const viewKey = normalizeSheetReferenceToken(candidate.contentsViewName);
	            if (areSheetReferenceKeysCompatible(pkgKey, packageKey)) {
	                return true;
	            }
	            return areSheetReferenceKeysCompatible(pkgKey, viewKey);
	        });
	    }
	    function exactSheetSymbolPackageMetadata(catalog, pkgName) {
	        if (!pkgName) {
	            return undefined;
	        }
	        return catalog.get(pkgName) ?? catalog.get(stripNormalViewSuffix(pkgName));
	    }
	    function sheetReferenceKeysMatchDirectly(left, right) {
	        if (!left || !right) {
	            return false;
	        }
	        if (left === right) {
	            return true;
	        }
	        return left.length >= 5 && right.length >= 5 && (left.startsWith(right) || right.startsWith(left));
	    }
	    function compactSheetReferenceKeysMatch(compactLeft, compactRight) {
	        if (compactLeft === compactRight) {
	            return true;
	        }
	        return compactLeft.startsWith(compactRight) || compactRight.startsWith(compactLeft);
	    }
	    function areSheetReferenceKeysCompatible(left, right) {
	        if (sheetReferenceKeysMatchDirectly(left, right)) {
	            return true;
	        }
	        if (!left || !right) {
	            return false;
	        }
	        return compactSheetReferenceKeysMatch(left.replace(/PORT/g, ''), right.replace(/PORT/g, ''));
	    }
	    function normalizedSheetModuleInstanceKey(value) {
	        return normalizeSheetReferenceToken(value).replace(/^X\d+/, '').replace(/\d+$/, '');
	    }
	    function normalizeSheetReferenceToken(value) {
	        return (value ?? '')
	            .trim()
	            .replace(/\.(?:dsn|xml)$/i, '')
	            .replace(/\.(?:Normal|IEEE|Convert)$/i, '')
	            .replace(/[^A-Za-z0-9]+/g, '')
	            .toUpperCase();
	    }
	    function nonEmpty(value) {
	        if (!value || value === OrcadDsnCfbMaterializer_1.PENDING_CONTENTS_VIEW_NAME) {
	            return undefined;
	        }
	        return value;
	    }
	    function buildTitleBlock(object, pageCount) {
	        const displayProperties = (object.displayProperties ?? []).map(titleBlockDisplayPropertyFromCfb);
	        const userProperties = userPropertiesFromCfb(object.userProperties).sort((left, right) => left.Name < right.Name ? -1
	            : left.Name > right.Name ? 1
	                : 0);
	        return {
	            Color: object.color,
	            DbId: object.dbId,
	            LibName: object.resolvedMetadata?.libName ?? '',
	            LocX: object.locX,
	            LocY: object.locY,
	            Name: object.symbolName,
	            Rotation: object.resolvedMetadata?.rotation ?? 0,
	            SymbolName: object.symbolName,
	            KeepInLowerRightCorner: true,
	            PageCount: pageCount,
	            PageNumber: 0,
	            ...(displayProperties.length > 0 ? { DisplayProperty: displayProperties } : {}),
	            ...(userProperties.length > 0 ? { UserProperty: userProperties } : {}),
	        };
	    }
	    function buildGlobal(object, pageObjectNetNames = new Map(), diagnostics) {
	        const displayProperties = (object.displayProperties ?? []).map((property) => globalDisplayPropertyFromCfb(property, diagnostics));
	        const attachedNetName = pageObjectNetNames.get(object.dbId);
	        const name = object.symbolName === '0' ?
	            object.symbolName
	            : firstPlausiblePlacedSymbolNetName(object, [attachedNetName, object.symbolName], diagnostics);
	        const normalizedDisplayProperties = addSyntheticIrefDisplayProperty(displayProperties, object, name, literalIrefValue(object), diagnostics);
	        const userProperties = userPropertiesFromCfb(object.userProperties ?? []);
	        if (object.symbolName === '0' &&
	            attachedNetName &&
	            plausibleNetNameCandidate(object, attachedNetName, diagnostics)) {
	            userProperties.push({ Name: 'NODENAME', Value: attachedNetName });
	        }
	        return {
	            Color: object.color,
	            DbId: object.dbId,
	            LibName: object.resolvedMetadata?.libName ?? '',
	            LocX: object.locX,
	            LocY: object.locY,
	            Mirror: object.resolvedMetadata?.mirror ?? 0,
	            Name: name,
	            Rotation: object.resolvedMetadata?.rotation ?? 0,
	            SymbolName: object.symbolName,
	            ...(normalizedDisplayProperties[0] ? { DisplayProperty: normalizedDisplayProperties[0] } : {}),
	            ...(userProperties.length ? { UserProperty: userProperties } : {}),
	        };
	    }
	    function drawnInstBoundsFromPorts(object) {
	        const ports = object.materializedPortInstances ?? [];
	        if (ports.length === 0) {
	            return {
	                X1: object.relativeDisplayX1,
	                X2: object.relativeDisplayX2,
	                Y1: object.relativeDisplayY1,
	                Y2: object.relativeDisplayY2,
	            };
	        }
	        const maxPortY = Math.max(0, ...ports.map((port) => port.hotptY ?? port.startY ?? 0));
	        let snappedY2 = Math.floor(object.relativeDisplayY2 / 10) * 10;
	        if (snappedY2 - maxPortY <= 10) {
	            snappedY2 = maxPortY;
	        }
	        else if (maxPortY <= snappedY2 - 50) {
	            snappedY2 -= 10;
	        }
	        return {
	            X1: Math.min(0, ...ports.map((port) => port.hotptX ?? port.startX ?? 0)),
	            X2: Math.max(0, ...ports.map((port) => port.hotptX ?? port.startX ?? 0), Math.floor(object.relativeDisplayX2 / 10) * 10),
	            Y1: Math.min(0, ...ports.map((port) => port.hotptY ?? port.startY ?? 0)),
	            Y2: Math.max(maxPortY, snappedY2),
	        };
	    }
	    function buildDrawnInstPortDbIdLookup(objects, diagnostics) {
	        const ids = new WeakMap();
	        for (const owner of objects.filter(isDrawnInst)) {
	            const frame = owner.recordFrame;
	            const connections = objects
	                .filter(isPortInstance)
	                .filter((pin) => frame !== undefined && pin.offset >= frame.offset && pin.offset < frame.endOffset);
	            const ports = owner.materializedPortInstances ?? [];
	            const candidates = ports.map((port) => connections.filter((pin) => owner.rotation === 0 &&
	                owner.mirror === 0 &&
	                port.hotptX !== undefined &&
	                port.hotptY !== undefined &&
	                pin.x === owner.locX + port.hotptX &&
	                pin.y === owner.locY + port.hotptY));
	            ids.set(owner, candidates.map((matches, index) => {
	                const pin = matches.length === 1 ? matches[0] : undefined;
	                if (pin &&
	                    candidates.filter((other) => other.some((candidate) => candidate.pinIndex === pin.pinIndex)).length === 1) {
	                    return owner.dbId * 2 ** 16 + pin.pinIndex;
	                }
	                emitDecisionDiagnostic(diagnostics, 'drawn-port-identity-withheld', `owner=${owner.dbId} name=${JSON.stringify(ports[index].name)}: no unique framed local pin identity`, 'CFB drawing port', 'warning');
	                return undefined;
	            }));
	        }
	        return ids;
	    }
	    function buildOffPageConnector(object, pageObjectNetNames = new Map(), objects = [], diagnostics) {
	        const displayProperties = connectorDisplayPropertiesFromCfb(object.displayProperties, object.dbId, diagnostics);
	        const name = firstPlausiblePlacedSymbolNetName(object, [
	            pageObjectNetNames.get(object.dbId),
	            object.reference,
	            object.partValue,
	            object.resolvedMetadata?.reference,
	            object.resolvedMetadata?.partValue,
	        ], diagnostics);
	        const namedDisplayProperties = addSyntheticNameDisplayProperty(displayProperties, object, name, diagnostics);
	        const normalizedDisplayProperties = addSyntheticIrefDisplayProperty(namedDisplayProperties, object, name, literalIrefValue(object), diagnostics);
	        const userProperties = userPropertiesFromCfb(object.userProperties);
	        return {
	            Color: object.color,
	            DbId: object.dbId,
	            LibName: object.resolvedMetadata?.libName ?? '',
	            LocX: object.locX,
	            LocY: object.locY,
	            Mirror: object.resolvedMetadata?.mirror ?? 0,
	            Name: name,
	            Rotation: object.resolvedMetadata?.rotation ??
	                (/^OFFPAGELEFT-R$/i.test(object.symbolName) ? inferOffPageConnectorRotation(object, objects) : 0),
	            SymbolName: object.symbolName,
	            ...(normalizedDisplayProperties.length > 0 ? { OffPageConnectorDisplayProp: normalizedDisplayProperties } : {}),
	            ...(userProperties.length ? { OffPageConnectorUserProp: userProperties } : {}),
	        };
	    }
	    function inferOffPageConnectorRotation(object, objects) {
	        if (!/^OFFPAGELEFT-R$/i.test(object.symbolName)) {
	            return 0;
	        }
	        const wires = objects.filter(isWire);
	        if (findExactPointOwner(object.locX, object.locY + 10, wires)) {
	            return 2;
	        }
	        return 0;
	    }
	    function buildPort(object, pageObjectNetNames = new Map(), diagnostics) {
	        const displayProperties = connectorDisplayPropertiesFromCfb(object.displayProperties, object.dbId, diagnostics);
	        const name = firstPlausiblePlacedSymbolNetName(object, [
	            pageObjectNetNames.get(object.dbId),
	            object.reference,
	            object.partValue,
	            object.resolvedMetadata?.reference,
	            object.resolvedMetadata?.partValue,
	        ], diagnostics);
	        const namedDisplayProperties = addSyntheticNameDisplayProperty(displayProperties, object, name, diagnostics);
	        const enrichedDisplayProperties = addSyntheticIrefDisplayProperty(namedDisplayProperties, object, name, literalIrefValue(object), diagnostics);
	        const userProperties = userPropertiesFromCfb(object.userProperties);
	        const rotation = object.resolvedMetadata?.rotation ?? 0;
	        const portType = portTypeFromSymbolName(object.symbolName);
	        return {
	            Color: object.color,
	            DbId: object.dbId,
	            LibName: object.resolvedMetadata?.libName ?? '',
	            LocX: object.locX,
	            LocY: object.locY,
	            Mirror: object.resolvedMetadata?.mirror ?? 0,
	            Name: name,
	            Rotation: rotation,
	            SymbolName: object.symbolName,
	            ...(portType === undefined ? {} : { Type: portType }),
	            ...(enrichedDisplayProperties.length > 0 ? { PortDisplayProp: enrichedDisplayProperties } : {}),
	            ...(userProperties.length ? { PortUserProp: userProperties } : {}),
	        };
	    }
	    function portTypeFromSymbolName(symbolName) {
	        if (/^PORTNO-/i.test(symbolName)) {
	            return 7;
	        }
	        return undefined;
	    }
	    function compareNamedPageObjects(left, right) {
	        const byName = compareOrcadName(left.Name, right.Name);
	        return byName || left.DbId - right.DbId;
	    }
	    function compareOrcadName(left, right) {
	        return (left < right ? -1
	            : left > right ? 1
	                : 0);
	    }
	    function comparePageObjectDbId(left, right) {
	        return left.dbId - right.dbId;
	    }
	    function buildOccurrences(schematics, rootSchematicName, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, occurrenceNetSystemIds, schematicViewNames, diagnostics) {
	        const rootSchematic = schematics.find((schematic) => schematic.Name === rootSchematicName) ?? schematics[0];
	        const rootOccurrence = {
	            RefInstId: '-1',
	            TitleBlockOccurrence: rootSchematic ? buildTitleBlockOccurrencesForSchematic(rootSchematic) : [],
	            InstOccurrence: rootSchematic ?
	                buildChildOccurrencesForSchematic(rootSchematic, schematics, new Set(), partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames)
	                : [],
	        };
	        const rootView = rootSchematic === undefined ? undefined : schematicViewNames.get(rootSchematic);
	        const roots = rootView === undefined ? [] : (hierarchyOccurrenceCatalog.rootsByView.get(rootView.toUpperCase()) ?? []);
	        if (roots.length === 1) {
	            applyHierarchyOccurrencePath(rootOccurrence, roots[0], hierarchyOccurrenceCatalog, diagnostics);
	        }
	        else if (roots.length > 1) {
	            emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', 'occurrence root has multiple counted paths', 'CFB hierarchy occurrence', 'warning');
	        }
	        attachOccurrenceNetSystemIds(rootOccurrence, occurrenceNetSystemIds);
	        return { InstOccurrence: [rootOccurrence] };
	    }
	    function applyHierarchyOccurrencePath(occurrence, branch, catalog, diagnostics) {
	        if (branch.occurrenceId !== undefined) {
	            occurrence.UserProperty = [
	                ...(occurrence.UserProperty ?? []).filter((property) => property.Name !== 'ID'),
	                { Name: 'ID', Value: String(branch.occurrenceId) },
	            ];
	        }
	        const children = occurrence.InstOccurrence ?? [];
	        const byPlacement = catalog.referencesByBranch.get(JSON.stringify([branch.streamPath, branch.offset]));
	        for (const [id, rows] of byPlacement ?? []) {
	            if (withholdHierarchyAdoption(diagnostics, catalog.trust, branch.streamPath, branch.viewName, id, 'occurrence-path', branch.offset)) {
	                continue;
	            }
	            const reference = agreeingHierarchyAnnotation(rows, diagnostics, JSON.stringify([branch.streamPath, branch.offset, id]));
	            const matches = children.filter((child) => child.RefInstId === String(reference.refInstId));
	            if (matches.length > 1) {
	                emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(branch.streamPath)}, branch=${branch.offset}, dbId=${reference.refInstId}: occurrence placement is not unique`, 'CFB hierarchy occurrence', 'warning');
	                continue;
	            }
	            if (matches.length === 0) {
	                children.push(instOccurrenceFromHierarchyReference(reference));
	            }
	            else {
	                const properties = instOccurrenceFromHierarchyReference(reference).UserProperty ?? [];
	                matches[0].UserProperty = [
	                    ...(matches[0].UserProperty ?? []).filter((property) => !properties.some((replacement) => replacement.Name === property.Name)),
	                    ...properties,
	                ];
	            }
	        }
	        occurrence.InstOccurrence = children;
	        for (const child of children) {
	            const paths = catalog.childrenByParentAndPlacement.get(JSON.stringify([branch.streamPath, branch.offset, child.RefInstId])) ??
	                [];
	            if (paths.length === 1) {
	                applyHierarchyOccurrencePath(child, paths[0], catalog, diagnostics);
	            }
	            else if (paths.length > 1) {
	                emitDecisionDiagnostic(diagnostics, 'hierarchy-adoption-withheld', `stream=${JSON.stringify(branch.streamPath)}, branch=${branch.offset}, dbId=${child.RefInstId}: occurrence child has multiple counted paths`, 'CFB hierarchy occurrence', 'warning');
	            }
	        }
	    }
	    function attachOccurrenceNetSystemIds(occurrence, catalog) {
	        const dbId = Number.parseInt(occurrence.RefInstId, 10);
	        const byName = Number.isNaN(dbId) ? undefined : catalog.get(dbId);
	        if (byName && byName.size > 0) {
	            occurrence.UserProperty = [
	                ...(occurrence.UserProperty ?? []),
	                ...Array.from(byName, ([name, id]) => ({
	                    Name: `${LogicalSchema_1.OrCadDSN.OCCURRENCE_NET_SYSTEM_ID_PROP_PREFIX}${name}`,
	                    Value: String(id),
	                })),
	            ];
	        }
	        for (const child of occurrence.InstOccurrence ?? []) {
	            attachOccurrenceNetSystemIds(child, catalog);
	        }
	    }
	    function buildChildOccurrencesForSchematic(schematic, allSchematics, visited, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames) {
	        const occurrences = [];
	        const viewName = schematicViewNames.get(schematic);
	        if (viewName === undefined) {
	            throw new Error('Converted schematic has no decoded folder identity');
	        }
	        for (const page of schematic.Page) {
	            for (const drawnInst of page.DrawnInst ?? []) {
	                if (!drawnInst.ContentsViewName && !drawnInst.ContentsLibName) {
	                    continue;
	                }
	                const target = targetSchematicForSheetReference(drawnInst, allSchematics);
	                const key = `drawn:${drawnInst.DbId}:${target?.Name ?? ''}`;
	                const nextVisited = new Set(visited);
	                nextVisited.add(key);
	                const children = target && !visited.has(key) ?
	                    buildChildOccurrencesForSchematic(target, allSchematics, nextVisited, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames)
	                    : [];
	                occurrences.push(instOccurrenceFromDrawnInst(drawnInst, target, children, page));
	            }
	            for (const partInst of page.PartInst ?? []) {
	                const target = partInst.ContentsViewType === 1 ? targetSchematicForSheetReference(partInst, allSchematics) : undefined;
	                const key = `part:${partInst.DbId}:${target?.Name ?? ''}`;
	                const nextVisited = new Set(visited);
	                nextVisited.add(key);
	                const children = target && !visited.has(key) ?
	                    buildChildOccurrencesForSchematic(target, allSchematics, nextVisited, partInstanceOccurrenceReferences, hierarchyOccurrenceCatalog, schematicViewNames)
	                    : [];
	                const occurrenceReference = partInstanceOccurrenceReferences.get(partInst);
	                occurrences.push(instOccurrenceFromPartInst(partInst, target, children, page, occurrenceReference));
	            }
	        }
	        return occurrences;
	    }
	    function instOccurrenceFromHierarchyReference(reference) {
	        return {
	            RefInstId: String(reference.refInstId),
	            UserProperty: [
	                ...(reference.occurrenceId !== undefined ? [{ Name: 'ID', Value: String(reference.occurrenceId) }] : []),
	                { Name: 'Part Reference', Value: reference.reference },
	                { Name: 'Reference', Value: reference.reference },
	            ],
	            InstOccurrence: [],
	        };
	    }
	    function targetSchematicForSheetReference(instance, allSchematics) {
	        const exact = allSchematics.find((candidate) => candidate.Name === instance.ContentsViewName);
	        if (exact) {
	            return exact;
	        }
	        const nameTokens = [instance.Name, instance.PartValue, instance.ContentsViewName].filter(Boolean).join(' ');
	        if (/pwm/i.test(nameTokens)) {
	            const pwmTargets = allSchematics.filter((candidate) => /pwm/i.test(candidate.Name));
	            if (pwmTargets.length === 1) {
	                return pwmTargets[0];
	            }
	        }
	        return undefined;
	    }
	    function storedPageNumber(titleBlocks) {
	        const numbers = new Set();
	        for (const titleBlock of titleBlocks ?? []) {
	            for (const property of titleBlock.UserProperty ?? []) {
	                if (property.Name !== 'Page Number') {
	                    continue;
	                }
	                if (!/^\+?\d+$/.test(property.Value.trim())) {
	                    return undefined;
	                }
	                const parsed = Number.parseInt(property.Value, 10);
	                if (parsed <= 0) {
	                    return undefined;
	                }
	                numbers.add(parsed);
	            }
	        }
	        const [only] = numbers;
	        return numbers.size === 1 && only !== undefined ? only : undefined;
	    }
	    function buildTitleBlockOccurrencesForSchematic(schematic) {
	        const occurrences = [];
	        for (const page of schematic.Page) {
	            const pageNumber = storedPageNumber(page.TitleBlock);
	            occurrences.push({
	                UserProperty: [
	                    { Name: 'Schematic Path', Value: `/${schematic.Name}/${page.Name}` },
	                    ...(pageNumber === undefined ?
	                        []
	                        : [
	                            { Name: 'Page Number', Value: String(pageNumber) },
	                            { Name: 'Schematic Page Number', Value: String(pageNumber) },
	                        ]),
	                ],
	            });
	        }
	        return occurrences;
	    }
	    function instOccurrenceFromDrawnInst(drawnInst, target, children, page) {
	        const occurrenceReference = drawnInst.Reference === '' ? drawnInst.DeviceDesignator : drawnInst.Reference;
	        return {
	            RefInstId: String(drawnInst.DbId),
	            UserProperty: [
	                { Name: 'Reference', Value: occurrenceReference },
	                { Name: 'Part Reference', Value: occurrenceReference },
	                { Name: 'Value', Value: drawnInst.PartValue },
	            ],
	            PortOccurrence: portOccurrencesFromPorts(drawnInst.PortInstScalar, drawnInst.PortInstBus, page),
	            TitleBlockOccurrence: target ? buildTitleBlockOccurrencesForSchematic(target) : [],
	            InstOccurrence: children,
	        };
	    }
	    function instOccurrenceFromPartInst(partInst, target, children, page, occurrenceReference) {
	        const reference = occurrenceReference?.reference ?? partInst.Reference;
	        return {
	            RefInstId: String(partInst.DbId),
	            UserProperty: [
	                { Name: 'Reference', Value: reference },
	                { Name: 'Part Reference', Value: reference },
	                { Name: 'Value', Value: partInst.PartValue },
	            ],
	            PortOccurrence: portOccurrencesFromPorts(partInst.PortInstScalar, partInst.PortInstBus, page),
	            TitleBlockOccurrence: target ? buildTitleBlockOccurrencesForSchematic(target) : [],
	            InstOccurrence: children,
	        };
	    }
	    function portOccurrencesFromPorts(scalarPorts, busPorts, page) {
	        const occurrences = [];
	        for (const port of scalarPorts ?? []) {
	            occurrences.push(portOccurrenceForPort(port));
	        }
	        for (const port of busPorts ?? []) {
	            const baseOccurrence = portOccurrenceForPort(port);
	            occurrences.push(baseOccurrence);
	            for (const memberName of busMemberNamesForPort(page, port.Name)) {
	                occurrences.push({
	                    KeyType: 'PortName',
	                    RefBusMemberName: memberName,
	                    RefKey: baseOccurrence.RefKey,
	                    RefPortName: memberName,
	                });
	            }
	        }
	        const sorted = occurrences.sort(comparePortOccurrencesByXmlRefKey);
	        return (0, portOccurrenceNormalization_1.normalizePortOccurrences)(sorted).portOccurrences;
	    }
	    function portOccurrenceForPort(port) {
	        return {
	            KeyType: 'PinPosition',
	            RefBusMemberName: '',
	            RefKey: 'Position' in port ? port.Position : port.Name,
	            RefPortName: port.Name,
	        };
	    }
	    function comparePortOccurrencesByXmlRefKey(left, right) {
	        const leftKey = numericOccurrenceRefKey(left.RefKey);
	        const rightKey = numericOccurrenceRefKey(right.RefKey);
	        if (leftKey !== rightKey) {
	            return leftKey - rightKey;
	        }
	        return compareXmlTextOrder(String(left.RefPortName), String(right.RefPortName));
	    }
	    function numericOccurrenceRefKey(refKey) {
	        const parsed = typeof refKey === 'number' ? refKey : Number(refKey);
	        return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
	    }
	    function busMemberNamesForPort(page, portName) {
	        const catalog = pageBusMemberCatalog(page);
	        const catalogNames = catalog.get(portName);
	        if (catalogNames?.length) {
	            return [...catalogNames];
	        }
	        const range = busNameDetails(portName);
	        if (!range || /\d$/.test(range.prefix)) {
	            return [];
	        }
	        const step = range.start <= range.end ? 1 : -1;
	        const names = [];
	        for (let index = range.start; step > 0 ? index <= range.end : index >= range.end; index += step) {
	            names.push(`${range.prefix}${index}`);
	        }
	        return names;
	    }
	    function pageBusMemberCatalog(page) {
	        const catalog = new Map();
	        for (const netBus of page.NetBus ?? []) {
	            const memberNames = (netBus.NetScalar ?? []).map((netScalar) => netScalar.Name).filter(Boolean);
	            if (memberNames.length === 0) {
	                continue;
	            }
	            const names = new Set();
	            if (netBus.Name) {
	                names.add(netBus.Name);
	            }
	            for (const wire of netBus.WireBus ?? []) {
	                if (wire.Name) {
	                    names.add(wire.Name);
	                }
	                for (const alias of wire.Alias ?? []) {
	                    if (alias.Name) {
	                        names.add(alias.Name);
	                    }
	                }
	            }
	            for (const name of names) {
	                catalog.set(name, memberNames);
	            }
	        }
	        return catalog;
	    }
	    function primitiveGeometry(primitives) {
	        const geometry = {};
	        for (const primitive of primitives) {
	            switch (primitive.kind) {
	                case 'line':
	                    pushValue(geometry, 'Line', lineFromPrimitive(primitive));
	                    break;
	                case 'rect':
	                    pushValue(geometry, 'Rectangle', rectangleFromPrimitive(primitive));
	                    break;
	                case 'ellipse':
	                    pushValue(geometry, 'Ellipse', ellipseFromPrimitive(primitive));
	                    break;
	                case 'polygon':
	                    pushValue(geometry, 'Polygon', polygonFromPrimitive(primitive));
	                    break;
	                case 'polyline':
	                    pushValue(geometry, 'Polyline', polylineFromPrimitive(primitive));
	                    break;
	                case 'arc':
	                    pushValue(geometry, 'Arc', arcFromPrimitive(primitive));
	                    break;
	                case 'comment-text':
	                    pushValue(geometry, 'CommentText', commentTextFromPrimitive(primitive, { useExactTextBoxHeight: true }));
	                    break;
	                case 'bitmap':
	                    pushValue(geometry, 'Bitmap', bitmapFromPrimitive(primitive));
	                    break;
	            }
	        }
	        return geometry;
	    }
	    function buildPagePrimitiveGraphics(primitives, textOptions = {}) {
	        const page = {};
	        for (const primitive of primitives) {
	            switch (primitive.kind) {
	                case 'line':
	                    pushValue(page, 'GraphicLineInst', {
	                        Color: DEFAULT_COLOR_VALUE,
	                        DbId: dbIdFromOffset(primitive.offset, 10),
	                        EndX: primitive.x2,
	                        EndY: primitive.y2,
	                        LineStyle: primitive.lineStyle,
	                        LineWidth: primitive.lineWidth,
	                        LocX: primitive.x1,
	                        LocY: primitive.y1,
	                        Name: `I${dbIdFromOffset(primitive.offset, 10)}`,
	                        Rotation: 0,
	                        StartX: primitive.x1,
	                        StartY: primitive.y1,
	                    });
	                    break;
	                case 'rect':
	                    pushValue(page, 'GraphicBoxInst', {
	                        Color: DEFAULT_COLOR_VALUE,
	                        DbId: dbIdFromOffset(primitive.offset, 20),
	                        FillStyle: primitive.fillStyle,
	                        HatchStyle: primitive.hatchStyle,
	                        LineStyle: primitive.lineStyle,
	                        LineWidth: primitive.lineWidth,
	                        LocX: primitive.x1,
	                        LocY: primitive.y1,
	                        Name: `I${dbIdFromOffset(primitive.offset, 20)}`,
	                        Rotation: 0,
	                        X1: primitive.x1,
	                        X2: primitive.x2,
	                        Y1: primitive.y1,
	                        Y2: primitive.y2,
	                    });
	                    break;
	                case 'ellipse':
	                    pushValue(page, 'GraphicEllipseInst', {
	                        Color: DEFAULT_COLOR_VALUE,
	                        DbId: dbIdFromOffset(primitive.offset, 30),
	                        FillStyle: primitive.fillStyle,
	                        HatchStyle: primitive.hatchStyle,
	                        LineStyle: primitive.lineStyle,
	                        LineWidth: primitive.lineWidth,
	                        LocX: primitive.x1,
	                        LocY: primitive.y1,
	                        Name: `I${dbIdFromOffset(primitive.offset, 30)}`,
	                        Rotation: 0,
	                        X1: primitive.x1,
	                        X2: primitive.x2,
	                        Y1: primitive.y1,
	                        Y2: primitive.y2,
	                    });
	                    break;
	                case 'polygon':
	                    pushValue(page, 'GraphicPolygonInst', {
	                        Color: DEFAULT_COLOR_VALUE,
	                        DbId: dbIdFromOffset(primitive.offset, 40),
	                        FillStyle: primitive.fillStyle,
	                        HatchStyle: primitive.hatchStyle,
	                        LineStyle: primitive.lineStyle,
	                        LineWidth: primitive.lineWidth,
	                        LocX: primitive.points[0]?.x ?? 0,
	                        LocY: primitive.points[0]?.y ?? 0,
	                        Name: `I${dbIdFromOffset(primitive.offset, 40)}`,
	                        Rotation: 0,
	                        PolygonPoint: primitive.points.map((point) => ({ X: point.x, Y: point.y })),
	                    });
	                    break;
	                case 'comment-text':
	                    pushValue(page, 'GraphicCommentTextInst', commentTextFromPrimitive(primitive, textOptions));
	                    break;
	                case 'bitmap':
	                    pushValue(page, 'GraphicBitMapInst', {
	                        DbId: primitive.dbId ?? dbIdFromOffset(primitive.offset, 50),
	                        LocX: primitive.locX,
	                        LocY: primitive.locY,
	                        Value: bitmapValueFromPrimitive(primitive),
	                        X1: primitive.locX,
	                        X2: primitive.x2,
	                        Y1: primitive.locY,
	                        Y2: primitive.y2,
	                    });
	                    break;
	            }
	        }
	        return page;
	    }
	    function groupSymbolObjects(stream) {
	        const groups = [];
	        let current = {
	            name: stream.parsed?.kind === 'package' ? stream.parsed.packageName
	                : stream.parsed?.kind === 'symbol' ? stream.parsed.symbolName
	                    : undefined,
	            records: [],
	            primitives: stream.decodedPrimitives ?? [],
	        };
	        for (const record of stream.decodedSymbolObjects ?? []) {
	            if (record.kind === 'view-header') {
	                if (current.records.length > 0) {
	                    groups.push(current);
	                }
	                current = { name: record.name, records: [record], primitives: stream.decodedPrimitives ?? [] };
	                continue;
	            }
	            current.records.push(record);
	        }
	        if (current.records.length > 0 || current.primitives.length > 0) {
	            groups.push(current);
	        }
	        return groups.length > 0 ? groups : [{ records: [], primitives: stream.decodedPrimitives ?? [] }];
	    }
	    function pageSizeInfoFromCfb(pageSizeInfo) {
	        return {
	            IsMetric: pageSizeInfo?.isMetric ?? false,
	            PinToPin: pageSizeInfo?.pinToPin ?? 100,
	            Height: pageSizeInfo?.height ?? 11700,
	            Width: pageSizeInfo?.width ?? 16500,
	        };
	    }
	    function pageDateTime(page, diagnostics) {
	        return dateTimeFromEpoch(page.pageDateTime?.modifyDateEpoch ?? page.pageDateTime?.createDateEpoch ?? 0, diagnostics);
	    }
	    function dateTimeFromEpoch(epoch = 0, diagnostics) {
	        if (!isRepresentableEpoch(epoch)) {
	            if (epoch !== 0 && diagnostics) {
	                emitBudgetedDiagnostic(diagnostics, 'date-time', 'page-date-out-of-range', `epoch=${epoch}`, 'CFB page', 'warning');
	            }
	            return {
	                CreateDate: '',
	                CreateDateEpoch: 0,
	                CreateTime: '',
	                ModifyDate: '',
	                ModifyDateEpoch: 0,
	                ModifyTime: '',
	            };
	        }
	        const stamp = new Date(epoch * 1000).toISOString();
	        return {
	            CreateDate: stamp.slice(0, 10),
	            CreateDateEpoch: epoch,
	            CreateTime: stamp.slice(11, 19),
	            ModifyDate: stamp.slice(0, 10),
	            ModifyDateEpoch: epoch,
	            ModifyTime: stamp.slice(11, 19),
	        };
	    }
	    function defaultValues(defaultFont = font()) {
	        return {
	            DefaultFont: [defaultFont],
	            DefaultPageRec: {
	                ANSIGridRefs: 1,
	                BorderDisplayed: 1,
	                BorderPrinted: 1,
	                GridRefDisplayed: 1,
	                GridRefPrinted: 1,
	                HorizontalLabelCount: 2,
	                HorizontalLabelIsAscending: 0,
	                HorizontalLabelIsChar: 0,
	                HorizontalLabelWidth: 10,
	                IsMetric: 0,
	                PinToPin: 100,
	                TitleBlockDisplayed: 1,
	                TitleBlockPrinted: 1,
	                VerticalLabelCount: 2,
	                VerticalLabelIsAscending: 0,
	                VerticalLabelIsChar: 1,
	                VerticalLabelWidth: 6,
	            },
	            DefaultPlacedInstIsPrimitive: 2,
	            DefaultDrawnInstIsPrimitive: 2,
	            DefaultPartFieldMappings: [],
	        };
	    }
	    function isRecord(value) {
	        return typeof value === 'object' && value !== null && !Array.isArray(value);
	    }
	    function pageCommentTextOptionsForDocument(document) {
	        const dsnStream = document.streams.find((stream) => stream.parsed?.kind === 'dsn-stream');
	        const json = dsnStream?.parsed?.kind === 'dsn-stream' && isRecord(dsnStream.parsed.json) ? dsnStream.parsed.json : undefined;
	        return {
	            defaultFontName: font().Name,
	            ...(json?.InstalledVersionBase !== undefined ? { useExactTextBoxHeight: true } : {}),
	        };
	    }
	    function font() {
	        return {
	            Charset: 0,
	            Escapement: 0,
	            Height: 8,
	            Italic: 0,
	            Name: '',
	            Orientation: 0,
	            Weight: 400,
	            Width: 3,
	        };
	    }
	    function arialFont(height) {
	        return {
	            Charset: 0,
	            Escapement: 0,
	            Height: height,
	            Italic: 0,
	            Name: 'Arial',
	            Orientation: 0,
	            Weight: 400,
	            Width: 0,
	        };
	    }
	    function color(value = DEFAULT_COLOR_VALUE) {
	        return { Value: value };
	    }
	    function displayType(value = DEFAULT_DISPLAY_TYPE_VALUE) {
	        return { ValueIfValueExist: 0, Value: value };
	    }
	    function lineFromPrimitive(primitive) {
	        return {
	            LineStyle: primitive.lineStyle,
	            LineWidth: primitive.lineWidth,
	            X1: primitive.x1,
	            X2: primitive.x2,
	            Y1: primitive.y1,
	            Y2: primitive.y2,
	        };
	    }
	    function rectangleFromPrimitive(primitive) {
	        return {
	            FillStyle: primitive.fillStyle,
	            HatchStyle: primitive.hatchStyle,
	            LineStyle: primitive.lineStyle,
	            LineWidth: primitive.lineWidth,
	            X1: primitive.x1,
	            X2: primitive.x2,
	            Y1: primitive.y1,
	            Y2: primitive.y2,
	        };
	    }
	    function ellipseFromPrimitive(primitive) {
	        return {
	            FillStyle: primitive.fillStyle,
	            HatchStyle: primitive.hatchStyle,
	            LineStyle: primitive.lineStyle,
	            LineWidth: primitive.lineWidth,
	            X1: primitive.x1,
	            X2: primitive.x2,
	            Y1: primitive.y1,
	            Y2: primitive.y2,
	        };
	    }
	    function polygonFromPrimitive(primitive) {
	        return {
	            FillStyle: primitive.fillStyle,
	            HatchStyle: primitive.hatchStyle,
	            LineStyle: primitive.lineStyle,
	            LineWidth: primitive.lineWidth,
	            PolygonPoint: primitive.points.map((point) => ({ X: point.x, Y: point.y })),
	        };
	    }
	    function polylineFromPrimitive(primitive) {
	        return {
	            LineStyle: primitive.lineStyle,
	            LineWidth: primitive.lineWidth,
	            PolylinePoint: primitive.points.map((point) => ({ X: point.x, Y: point.y })),
	        };
	    }
	    function arcFromPrimitive(primitive) {
	        return {
	            EndX: primitive.endX,
	            EndY: primitive.endY,
	            LineStyle: primitive.lineStyle,
	            LineWidth: primitive.lineWidth,
	            StartX: primitive.startX,
	            StartY: primitive.startY,
	            X1: primitive.x1,
	            X2: primitive.x2,
	            Y1: primitive.y1,
	            Y2: primitive.y2,
	        };
	    }
	    function commentTextFromPrimitive(primitive, options = {}) {
	        return {
	            DbId: dbIdFromOffset(primitive.offset, 60),
	            LocX: primitive.locX,
	            LocY: primitive.locY,
	            Name: primitive.name,
	            TextJustification: primitive.textFlags,
	            X1: primitive.x1,
	            X2: primitive.x2,
	            Y1: primitive.y1,
	            Y2: primitive.y2,
	            Font: fontFromTextBounds(primitive, options),
	            Color: DEFAULT_COLOR_VALUE,
	        };
	    }
	    function fontFromTextBounds(primitive, options = {}) {
	        const lineCount = commentTextLineCount(primitive.name);
	        const height = Math.abs(primitive.y2 - primitive.y1);
	        const effectiveHeight = options.useExactTextBoxHeight ? height : Math.max(1, height - 1);
	        return {
	            ...font(),
	            Height: -Math.round(effectiveHeight / lineCount),
	            Name: options.defaultFontName ?? font().Name,
	        };
	    }
	    function commentTextLineCount(name) {
	        const lines = name.split(/\r\n|\r|\n/);
	        if (lines.length > 1 && lines[lines.length - 1] === '') {
	            lines.pop();
	        }
	        return Math.max(1, lines.length);
	    }
	    function bitmapFromPrimitive(primitive) {
	        return {
	            LocX: primitive.locX,
	            LocY: primitive.locY,
	            Value: bitmapValueFromPrimitive(primitive),
	            X1: primitive.locX,
	            X2: primitive.x2,
	            Y1: primitive.locY,
	            Y2: primitive.y2,
	        };
	    }
	    function bitmapValueFromPrimitive(primitive) {
	        const payload = primitive.dibPayload;
	        if (!payload?.length) {
	            return '';
	        }
	        if (payload[0] === 0x42 && payload[1] === 0x4d) {
	            return (0, base64_1.uint8ArrayToBase64)(payload);
	        }
	        return (0, base64_1.uint8ArrayToBase64)(prependBmpHeader(payload));
	    }
	    function prependBmpHeader(dib) {
	        const dibHeaderSize = readUInt32LE(dib, 0);
	        const fileHeaderSize = 14;
	        const fileSize = fileHeaderSize + dib.length;
	        const bmp = new Uint8Array(fileSize);
	        bmp[0] = 0x42;
	        bmp[1] = 0x4d;
	        writeUInt32LE(bmp, 2, fileSize);
	        writeUInt32LE(bmp, 10, fileHeaderSize + dibHeaderSize);
	        bmp.set(dib, fileHeaderSize);
	        return bmp;
	    }
	    function boundingBoxFromGeometry(geometry, options = {}) {
	        const points = [];
	        for (const line of geometry.Line ?? []) {
	            points.push({ X: line.X1, Y: line.Y1 }, { X: line.X2, Y: line.Y2 });
	        }
	        for (const rect of geometry.Rectangle ?? []) {
	            points.push({ X: rect.X1, Y: rect.Y1 }, { X: rect.X2, Y: rect.Y2 });
	        }
	        for (const ellipse of geometry.Ellipse ?? []) {
	            points.push({ X: ellipse.X1, Y: ellipse.Y1 }, { X: ellipse.X2, Y: ellipse.Y2 });
	        }
	        for (const polygon of geometry.Polygon ?? []) {
	            points.push(...polygon.PolygonPoint);
	        }
	        for (const polyline of geometry.Polyline ?? []) {
	            points.push(...polyline.PolylinePoint);
	        }
	        for (const arc of geometry.Arc ?? []) {
	            points.push(...arcExtentPoints(arc));
	        }
	        if (!options.excludeTextWhenPossible || points.length === 0) {
	            for (const text of geometry.CommentText ?? []) {
	                points.push({ X: text.X1, Y: text.Y1 }, { X: text.X2, Y: text.Y2 });
	            }
	        }
	        for (const bitmap of geometry.Bitmap ?? []) {
	            points.push({ X: bitmap.X1, Y: bitmap.Y1 }, { X: bitmap.X2, Y: bitmap.Y2 });
	        }
	        if (points.length === 0) {
	            return { X1: 0, X2: 0, Y1: 0, Y2: 0 };
	        }
	        return snapBoundingBox({
	            X1: Math.min(...points.map((point) => point.X)),
	            X2: Math.max(...points.map((point) => point.X)),
	            Y1: Math.min(...points.map((point) => point.Y)),
	            Y2: Math.max(...points.map((point) => point.Y)),
	        });
	    }
	    function arcExtentPoints(arc) {
	        const centerX = (arc.X1 + arc.X2) / 2;
	        const centerY = (arc.Y1 + arc.Y2) / 2;
	        const radiusX = (arc.X2 - arc.X1) / 2;
	        const radiusY = (arc.Y2 - arc.Y1) / 2;
	        const endpoints = [
	            { X: arc.StartX, Y: arc.StartY },
	            { X: arc.EndX, Y: arc.EndY },
	        ];
	        if (radiusX <= 0 || radiusY <= 0) {
	            return [
	                { X: arc.X1, Y: arc.Y1 },
	                { X: arc.X2, Y: arc.Y2 },
	            ];
	        }
	        const angleOf = (x, y) => {
	            const angle = Math.atan2((y - centerY) / radiusY, (x - centerX) / radiusX);
	            return angle < 0 ? angle + 2 * Math.PI : angle;
	        };
	        const startAngle = angleOf(arc.StartX, arc.StartY);
	        const clockwiseSweep = (startAngle - angleOf(arc.EndX, arc.EndY) + 2 * Math.PI) % (2 * Math.PI);
	        const sweep = arc.StartX === arc.EndX && arc.StartY === arc.EndY ? 2 * Math.PI
	            : clockwiseSweep === 0 ? 2 * Math.PI
	                : clockwiseSweep;
	        const extremes = [
	            { angle: 0, point: { X: centerX + radiusX, Y: centerY } },
	            { angle: Math.PI / 2, point: { X: centerX, Y: centerY + radiusY } },
	            { angle: Math.PI, point: { X: centerX - radiusX, Y: centerY } },
	            { angle: (3 * Math.PI) / 2, point: { X: centerX, Y: centerY - radiusY } },
	        ];
	        return [
	            ...endpoints,
	            ...extremes
	                .filter(({ angle }) => (startAngle - angle + 2 * Math.PI) % (2 * Math.PI) <= sweep)
	                .map(({ point }) => point),
	        ];
	    }
	    function snapBoundingBox(box) {
	        return {
	            X1: Math.floor(box.X1 / 10) * 10,
	            X2: Math.ceil(box.X2 / 10) * 10,
	            Y1: Math.floor(box.Y1 / 10) * 10,
	            Y2: Math.ceil(box.Y2 / 10) * 10,
	        };
	    }
	    function emitDerivedSymbolBoundingBoxDiagnostics(cache, options) {
	        if (!options.diagnostics) {
	            return;
	        }
	        for (const symbol of [...(cache.PortSymbol ?? []), ...(cache.OffPageSymbol ?? []), ...(cache.GlobalSymbol ?? [])]) {
	            options.diagnostics.push({
	                severity: 'info',
	                message: `CFB symbol [symbol-bbox-derived] name="${symbol.Name}" sourceLibName="${symbol.SourceLibName}"`,
	            });
	        }
	    }
	    function symbolPinScalarFromCfb(pin, position = 0) {
	        const type = pin.pinType;
	        const flags = pin.displayFlags;
	        const isSet = (flag) => flags !== undefined && (flags & flag) !== 0;
	        const powerPin = type === POWER_PIN_TYPE;
	        return {
	            HotptX: pin.hotptX,
	            HotptY: pin.hotptY,
	            Name: pin.name,
	            Position: position,
	            StartX: pin.startX,
	            StartY: pin.startY,
	            ...(type === undefined ? {} : { Type: type }),
	            ...(flags === undefined ?
	                {}
	                : {
	                    Visible: isSet(PinFlag.IsVisible) ? 1 : 0,
	                    IsLong: isSet(PinFlag.IsLong),
	                    IsLeftPointing: isSet(PinFlag.IsLeftPointing),
	                    IsRightPointing: isSet(PinFlag.IsRightPointing),
	                }),
	            IsClock: isSet(PinFlag.IsClock),
	            IsDot: isSet(PinFlag.IsDot),
	            IsNetStyle: false,
	            IsNoConnect: false,
	            IsGlobal: powerPin || isSet(PinFlag.IsHidden),
	        };
	    }
	    function offPageSymbolPinScalarFromCfb(pin, position = 0) {
	        const scalar = symbolPinScalarFromCfb(pin, position);
	        scalar.IsLong = true;
	        scalar.Visible = 0;
	        scalar.IsGlobal = true;
	        return scalar;
	    }
	    function globalSymbolPinScalarFromCfb(pin, position = 0) {
	        const scalar = symbolPinScalarFromCfb(pin, position);
	        scalar.Type = 7;
	        scalar.IsLong = true;
	        scalar.IsLeftPointing = false;
	        scalar.IsRightPointing = false;
	        scalar.Visible = 0;
	        scalar.IsGlobal = true;
	        return scalar;
	    }
	    function portInstsFromMaterializedPorts(ports, dbIdSeed, physicalPadPresenceCatalog, physicalPadKeys, diagnostics, hasDeclaredBounds) {
	        const scalar = [];
	        const bus = [];
	        for (let index = 0; index < ports.length; index++) {
	            const port = ports[index];
	            const position = port.position ?? index;
	            const requiresRecordedPosition = !hasDeclaredBounds || port.hotptX === undefined || port.hotptY === undefined;
	            if (requiresRecordedPosition && (port.x === undefined || port.y === undefined)) {
	                emitDecisionDiagnostic(diagnostics, 'placed-port-position-unresolved', `dbId=${dbIdSeed} position=${position} name="${port.name}": no recorded page coordinate or complete decoded pin transform (declaredBounds=${hasDeclaredBounds}); connectivity withheld`, 'CFB placed port', 'warning');
	            }
	            const physicalPadPresence = physicalPadPresenceAt(physicalPadPresenceCatalog, physicalPadKeys, ports.length, position);
	            const implicitShield = isImplicitShieldPort(port, physicalPadPresence);
	            const shieldPin = isShieldPinName(port.name);
	            const converted = {
	                RequiresRecordedPosition: requiresRecordedPosition,
	                IsPlacedInstPin: port.isPlacedInstPin ?? true,
	                DbId: dbIdSeed * 1000 + index,
	                HotptX: port.hotptX ?? 0,
	                HotptY: port.hotptY ?? 0,
	                Name: port.name,
	                Position: position,
	                StartX: port.startX ?? port.hotptX ?? 0,
	                StartY: port.startY ?? port.hotptY ?? 0,
	                Type: implicitShield ? 7 : (port.type ?? DEFAULT_PIN_TYPE),
	                Visible: (implicitShield ||
	                    (shieldPin &&
	                        port.type === 7 &&
	                        port.visible !== true &&
	                        !(physicalPadPresence === 'present' && port.isExplicit === true && hasResolvableWireGeometry(port)))) ?
	                    false
	                    : (port.visible ?? true),
	                IsNoConnect: shieldPin ? false : (port.isNoConnect ?? false),
	                ...(port.x !== undefined && port.y !== undefined ? { RecordedX: port.x, RecordedY: port.y } : {}),
	                ...(port.wireDbId !== undefined ? { RecordedWireDbId: port.wireDbId } : {}),
	            };
	            (port.isBus ? bus : scalar).push(converted);
	        }
	        return { scalar, bus };
	    }
	    function hasResolvableWireGeometry(port) {
	        return port.wireDbId !== undefined && port.x !== undefined && port.y !== undefined;
	    }
	    function isShieldPinName(name) {
	        return /^SHIELD\d*$/i.test(name);
	    }
	    function isImplicitShieldPort(port, physicalPadPresence) {
	        if (!isShieldPinName(port.name) || physicalPadPresence === 'present') {
	            return false;
	        }
	        if (physicalPadPresence === 'absent') {
	            return true;
	        }
	        if (port.visible !== undefined) {
	            return !port.visible;
	        }
	        if (port.isExplicit !== undefined) {
	            return !port.isExplicit;
	        }
	        return false;
	    }
	    function drawnPortInstsFromMaterializedPorts(ports, portDbIds) {
	        const scalar = [];
	        const bus = [];
	        for (let index = 0; index < ports.length; index++) {
	            const port = ports[index];
	            const dbId = portDbIds?.[index];
	            const converted = {
	                DbId: dbId,
	                HotptX: port.hotptX ?? 0,
	                HotptY: port.hotptY ?? 0,
	                Name: port.name,
	                StartX: port.startX ?? 0,
	                StartY: port.startY ?? 0,
	                Type: port.type ?? DEFAULT_PIN_TYPE,
	                Visible: port.visible ?? true,
	                IsLong: true,
	                IsClock: false,
	                IsDot: false,
	                IsLeftPointing: true,
	                IsRightPointing: true,
	                IsNetStyle: port.source === 'drawn-port',
	                IsGlobal: false,
	                IsNoConnect: port.isNoConnect,
	            };
	            (port.isBus ? bus : scalar).push(converted);
	        }
	        return { scalar, bus };
	    }
	    function partDisplayPropertyFromCfb(property) {
	        return {
	            DbId: dbIdFromOffset(property.offset, 70),
	            LocX: property.locX,
	            LocY: property.locY,
	            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
	            Rotation: displayPropertyRotation(property),
	            TextJustification: 0,
	            Font: font(),
	            Color: color(property.color),
	            DisplayType: displayType(displayPropertyDisplayType(property)),
	        };
	    }
	    function drawnInstDisplayPropertyFromCfb(property) {
	        return {
	            DbId: dbIdFromOffset(property.offset, 80),
	            LocX: property.locX,
	            LocY: property.locY,
	            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
	            Rotation: displayPropertyRotation(property),
	            TextJustification: 0,
	            Font: drawnInstDisplayPropertyFont(property),
	            Color: color(property.color),
	            DisplayType: displayType(displayPropertyDisplayType(property)),
	        };
	    }
	    function globalDisplayPropertyFromCfb(property, diagnostics) {
	        return {
	            DbId: dbIdFromOffset(property.offset, 90),
	            LocX: property.locX,
	            LocY: property.locY,
	            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
	            Rotation: displayPropertyRotation(property),
	            TextJustification: 0,
	            Font: connectorDisplayPropertyFont(property, diagnostics),
	            Color: color(property.color),
	            DisplayType: displayType(connectorDisplayPropertyDisplayType(property)),
	        };
	    }
	    function connectorDisplayPropertiesFromCfb(properties, dbId, diagnostics) {
	        const records = properties ?? [];
	        const named = records.filter((property) => displayPropertyLogicalName(property) !== undefined);
	        if (named.length > 0 && !named.some((property) => displayPropertyLogicalName(property) === 'Name')) {
	            emitBudgetedDiagnostic(diagnostics, `connector-display-name\0${dbId}`, 'connector-text-not-filed-under-name', `dbId=${dbId} records=${named.map((property) => displayPropertyLogicalName(property)).join(',')}`, 'CFB connector', 'warning');
	        }
	        return records.map((property) => connectorDisplayPropertyFromCfb(property, diagnostics));
	    }
	    function connectorDisplayPropertyFromCfb(property, diagnostics) {
	        return {
	            DbId: dbIdFromOffset(property.offset, 90),
	            LocX: property.locX,
	            LocY: property.locY,
	            Name: displayPropertyLogicalName(property) ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
	            Rotation: displayPropertyRotation(property),
	            TextJustification: 0,
	            Font: connectorDisplayPropertyFont(property, diagnostics),
	            Color: color(property.color),
	            DisplayType: displayType(connectorDisplayPropertyDisplayType(property)),
	        };
	    }
	    function titleBlockDisplayPropertyFromCfb(property) {
	        return {
	            DbId: dbIdFromOffset(property.offset, 100),
	            LocX: property.locX,
	            LocY: property.locY,
	            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
	            Rotation: property.rotation,
	            TextJustification: 0,
	            Font: font(),
	            Color: color(property.color),
	            DisplayType: displayType(property.displayType),
	        };
	    }
	    function symbolDisplayPropertyFromCfb(property) {
	        return {
	            LocX: property.locX,
	            LocY: property.locY,
	            Name: property.propertyName ?? UNNAMED_DISPLAY_PROPERTY_NAME(property.propertyCode),
	            Rotation: 0,
	            TextJustification: 0,
	            Font: font(),
	            Color: color(property.color),
	            DisplayType: displayType(property.displayType),
	        };
	    }
	    function addSyntheticIrefDisplayProperty(displayProperties, object, name, irefValue, diagnostics) {
	        if (!irefValue || displayProperties.some((property) => property.Name === 'IREF')) {
	            return displayProperties;
	        }
	        const displayProperty = syntheticIrefDisplayProperty(object, name, irefValue, diagnostics);
	        return displayProperty ? [...displayProperties, displayProperty] : displayProperties;
	    }
	    function addSyntheticNameDisplayProperty(displayProperties, object, name, diagnostics) {
	        if (displayProperties.some((property) => property.Name === 'Name')) {
	            return displayProperties;
	        }
	        const displayProperty = syntheticNameDisplayProperty(object, name, diagnostics);
	        return displayProperty ? [...displayProperties, displayProperty] : displayProperties;
	    }
	    function syntheticNameDisplayProperty(object, name, diagnostics) {
	        const locX = syntheticNameDisplayLocX(object, name, diagnostics);
	        if (locX === undefined) {
	            return undefined;
	        }
	        return {
	            DbId: dbIdFromOffset(object.offset, 89),
	            LocX: locX,
	            LocY: 6,
	            Name: 'Name',
	            Rotation: object.resolvedMetadata?.rotation ?? 0,
	            TextJustification: 0,
	            Font: font(),
	            Color: color(object.color),
	            DisplayType: displayType(),
	        };
	    }
	    function syntheticNameDisplayLocX(object, name, diagnostics) {
	        if (/-L$/i.test(object.symbolName)) {
	            return 32;
	        }
	        const labelFont = font();
	        const measurement = OrcadCaptureFontMetrics_1.OrcadCaptureFontMetrics.measure(name, labelFont);
	        if (measurement.advance === undefined) {
	            emitTextAdvanceDiagnostic(diagnostics, 'name-display-advance-unknown', labelFont, measurement.miss);
	            return undefined;
	        }
	        return -(measurement.advance + 1);
	    }
	    function syntheticIrefDisplayProperty(object, name, irefValue, diagnostics) {
	        const nameProperty = (object.displayProperties ?? []).find((property) => displayPropertyLogicalName(property) === 'Name');
	        if (!nameProperty) {
	            emitBudgetedDiagnostic(diagnostics, object.symbolName, 'iref-display-anchor-missing', `symbol "${object.symbolName}" carries no Name display record to place the cross-reference label against`, 'CFB text metric', 'warning');
	            return undefined;
	        }
	        const labelFont = connectorDisplayPropertyFont(nameProperty, diagnostics);
	        if (!carriesSampledDisplayTextStyle(nameProperty)) {
	            emitUndecodedTextStyleDiagnostic(diagnostics, 'iref-display-advance-unknown', nameProperty.textStyleId);
	            return undefined;
	        }
	        const irefLabelLeads = nameProperty.locX < 0;
	        const measurement = OrcadCaptureFontMetrics_1.OrcadCaptureFontMetrics.measure(irefLabelLeads ? irefValue : name, labelFont);
	        if (measurement.advance === undefined) {
	            emitTextAdvanceDiagnostic(diagnostics, 'iref-display-advance-unknown', labelFont, measurement.miss);
	            return undefined;
	        }
	        return {
	            DbId: dbIdFromOffset(nameProperty.offset, 91),
	            LocX: irefLabelLeads ? nameProperty.locX - measurement.advance : nameProperty.locX + measurement.advance,
	            LocY: nameProperty.locY,
	            Name: 'IREF',
	            Rotation: displayPropertyRotation(nameProperty),
	            TextJustification: 0,
	            Font: labelFont,
	            Color: color(nameProperty.color),
	            DisplayType: displayType(),
	        };
	    }
	    function emitTextAdvanceDiagnostic(state, reason, labelFont, miss) {
	        if (!miss) {
	            return;
	        }
	        const fontDescription = OrcadCaptureFontMetrics_1.OrcadCaptureFontMetrics.describeFont(labelFont);
	        const glyphDetail = miss.glyph === undefined ? '' : ` glyph "${miss.glyph}"`;
	        emitBudgetedDiagnostic(state, `${fontDescription}\0${miss.glyph ?? ''}`, reason, `${fontDescription}: ${miss.reason}${glyphDetail}`, 'CFB text metric', 'warning');
	    }
	    function displayPropertyLogicalName(property) {
	        return property.propertyName;
	    }
	    function drawnInstDisplayPropertyFont(property) {
	        return property.displayType === 46 || (property.displayType & 0x4000) !== 0 ? arialFont(-15) : font();
	    }
	    const SAMPLED_DISPLAY_TEXT_STYLE_ID = 1;
	    function carriesSampledDisplayTextStyle(property) {
	        return property.textStyleId === SAMPLED_DISPLAY_TEXT_STYLE_ID;
	    }
	    function emitUndecodedTextStyleDiagnostic(state, reason, textStyleId) {
	        const described = textStyleId === undefined ? 'absent' : String(textStyleId);
	        emitBudgetedDiagnostic(state, `text-style-${described}`, reason, `display text style ${described} carries no decoded width, weight or slant, so no measured row applies`, 'CFB text metric', 'warning');
	    }
	    function connectorDisplayPropertyFont(property, diagnostics) {
	        if (property.displayType === 36) {
	            return arialFont(-6);
	        }
	        if (property.displayType === 29) {
	            return arialFont(-7);
	        }
	        if (usesSmallConnectorTextStyle(property.propertyCode)) {
	            emitAssumedTextStyleDiagnostic(diagnostics, property.propertyCode);
	            return connectorSmallFont();
	        }
	        return font();
	    }
	    function usesSmallConnectorTextStyle(propertyCode) {
	        return propertyCode === 0x87;
	    }
	    function emitAssumedTextStyleDiagnostic(state, propertyCode) {
	        const code = `0x${propertyCode.toString(16)}`;
	        emitBudgetedDiagnostic(state, code, 'connector-text-style-assumed', `display code ${code} is read as the small port-label text style; the record carries no field that proves it`, 'CFB text metric', 'warning');
	    }
	    function connectorSmallFont() {
	        return {
	            Charset: 0,
	            Escapement: 0,
	            Height: -9,
	            Italic: 0,
	            Name: '',
	            Orientation: 0,
	            Weight: 400,
	            Width: 4,
	        };
	    }
	    function displayPropertyRotation(property) {
	        if ((property.displayType & 0x4000) !== 0) {
	            return 1;
	        }
	        if (property.displayType === 29 || property.displayType === 46) {
	            return 0;
	        }
	        return property.rotation;
	    }
	    function displayPropertyDisplayType(property) {
	        if ((property.displayType & 0x4000) !== 0) {
	            return DEFAULT_DISPLAY_TYPE_VALUE;
	        }
	        if (property.displayType === 29 || property.displayType === 46) {
	            return DEFAULT_DISPLAY_TYPE_VALUE;
	        }
	        return property.displayType;
	    }
	    function connectorDisplayPropertyDisplayType(property) {
	        if (property.textStyleId === 1 && property.displayType === 0) {
	            return DEFAULT_DISPLAY_TYPE_VALUE;
	        }
	        return displayPropertyDisplayType(property);
	    }
	    function partUserPropertiesFromCfbObject(object) {
	        return (object.userProperties ?? []).filter((property) => property.source !== 'display-property' || !['Part Reference', 'Value'].includes(property.name));
	    }
	    function userPropertiesFromCfb(properties) {
	        return (properties ?? []).map((property) => ({ Name: property.name, Value: property.value }));
	    }
	    function literalIrefValue(object) {
	        return (object.userProperties ?? []).find((property) => property.name === 'IREF')?.value;
	    }
	    function usablePlacedPartValue(value, cachePins) {
	        if (value === undefined || value.length === 0) {
	            return false;
	        }
	        return !cachePins?.some((pin) => pin.name.toUpperCase() === value.toUpperCase());
	    }
	    function normalizeCatalogValue(value) {
	        return /^\d+R$/i.test(value) ? value.slice(0, -1) : value;
	    }
	    function mergeUserProperties(...groups) {
	        const merged = new Map();
	        for (const property of groups.flat()) {
	            if (property.Name === 'Name') {
	                continue;
	            }
	            if (!merged.has(property.Name)) {
	                merged.set(property.Name, property);
	            }
	        }
	        return Array.from(merged.values());
	    }
	    function finalizePartUserProperties(properties) {
	        const byName = new Map(properties.map((property) => [property.Name, property.Value]));
	        return Array.from(byName, ([Name, Value]) => ({ Name, Value }));
	    }
	    function wireScalarFromCfb(wire, aliases, junctions, aliasFont = font()) {
	        return {
	            ...(wire.userProperties?.length ? { UserProperty: userPropertiesFromCfb(wire.userProperties) } : {}),
	            DbId: wire.dbId,
	            EndX: wire.endX,
	            EndY: wire.endY,
	            Name: '',
	            StartX: wire.startX,
	            StartY: wire.startY,
	            Color: color(wire.color),
	            LineStyle: wire.lineStyle ?? DEFAULT_LINE_STYLE,
	            LineWidth: wire.lineWidth ?? DEFAULT_LINE_WIDTH,
	            BoundingBox: boundingBoxFromWire(wire),
	            ...(aliases.length > 0 ? { Alias: aliases.map((alias) => aliasFromCfb(alias, aliasFont)) } : {}),
	            ...(junctions.length > 0 ? { Junction: junctions } : {}),
	        };
	    }
	    function wireBusFromCfb(wire, aliases, junctions, aliasFont = font(), _netGroupName) {
	        return {
	            DbId: wire.dbId,
	            EndX: wire.endX,
	            EndY: wire.endY,
	            Name: '',
	            StartX: wire.startX,
	            StartY: wire.startY,
	            Color: color(wire.color),
	            LineStyle: wire.lineStyle ?? DEFAULT_LINE_STYLE,
	            LineWidth: wire.lineWidth ?? DEFAULT_NET_LINE_WIDTH,
	            BoundingBox: boundingBoxFromWire(wire),
	            ...(aliases.length > 0 ? { Alias: aliases.map((alias) => aliasFromCfb(alias, aliasFont)) } : {}),
	            ...(junctions.length > 0 ? { Junction: junctions } : {}),
	        };
	    }
	    function aliasFromCfb(alias, aliasFont) {
	        return {
	            DbId: dbIdFromOffset(alias.offset, 110),
	            Color: alias.color,
	            LocX: alias.locX,
	            LocY: alias.locY,
	            Name: alias.name,
	            Rotation: alias.rotation,
	            Font: { ...aliasFont },
	        };
	    }
	    function boundingBoxFromWire(wire) {
	        return {
	            X1: Math.min(wire.startX, wire.endX),
	            X2: Math.max(wire.startX, wire.endX),
	            Y1: Math.min(wire.startY, wire.endY),
	            Y2: Math.max(wire.startY, wire.endY),
	        };
	    }
	    function wireNetIdentities(groups, aliasesByWireId, carriers, metadataNetNames) {
	        const identities = new Map();
	        for (let index = 0; index < groups.length; index++) {
	            const group = groups[index];
	            const aliasesInGroup = group.flatMap((wire) => aliasesByWireId.get(wire.dbId) ?? []);
	            const groupName = netNameForWireGroup(group, aliasesInGroup, carriers, metadataNetNames);
	            for (const wire of group) {
	                const ownName = metadataNetNames.get(wire.metadataId);
	                const name = ownName ?? (groupName === '' ? undefined : groupName);
	                identities.set(wire.dbId, { run: `run:${index}`, ...(name === undefined ? {} : { name }) });
	            }
	        }
	        return identities;
	    }
	    function groupConnectedWires(wires, wireState) {
	        const parent = new Map();
	        for (const wire of wires) {
	            parent.set(wire.dbId, wire.dbId);
	        }
	        const find = (id) => {
	            const current = parent.get(id) ?? id;
	            if (current === id) {
	                return id;
	            }
	            const root = find(current);
	            parent.set(id, root);
	            return root;
	        };
	        const union = (a, b) => {
	            const rootA = find(a);
	            const rootB = find(b);
	            if (rootA !== rootB) {
	                parent.set(rootB, rootA);
	            }
	        };
	        const endpointMap = new Map();
	        for (const wire of wires) {
	            for (const key of [coordKey(wire.startX, wire.startY), coordKey(wire.endX, wire.endY)]) {
	                const ids = endpointMap.get(key) ?? [];
	                ids.push(wire.dbId);
	                endpointMap.set(key, ids);
	            }
	        }
	        for (const ids of endpointMap.values()) {
	            for (let i = 1; i < ids.length; i++) {
	                union(ids[0], ids[i]);
	            }
	        }
	        for (let i = 0; i < wires.length; i++) {
	            for (let j = i + 1; j < wires.length; j++) {
	                const a = wires[i];
	                const b = wires[j];
	                if (pointOnWire(a.startX, a.startY, b) ||
	                    pointOnWire(a.endX, a.endY, b) ||
	                    pointOnWire(b.startX, b.startY, a) ||
	                    pointOnWire(b.endX, b.endY, a)) {
	                    union(a.dbId, b.dbId);
	                }
	            }
	        }
	        const aliasMap = new Map();
	        for (const wire of wires) {
	            for (const alias of wireState.get(wire.dbId)?.aliases ?? []) {
	                if (!alias.name) {
	                    continue;
	                }
	                const ids = aliasMap.get(alias.name) ?? [];
	                ids.push(wire.dbId);
	                aliasMap.set(alias.name, ids);
	            }
	            for (const groupName of wireState.get(wire.dbId)?.groupNames ?? []) {
	                if (!groupName) {
	                    continue;
	                }
	                const ids = aliasMap.get(netGroupKey(groupName)) ?? [];
	                ids.push(wire.dbId);
	                aliasMap.set(netGroupKey(groupName), ids);
	            }
	        }
	        for (const ids of aliasMap.values()) {
	            for (let i = 1; i < ids.length; i++) {
	                union(ids[0], ids[i]);
	            }
	        }
	        const groups = new Map();
	        for (const wire of wires) {
	            const root = find(wire.dbId);
	            const group = groups.get(root) ?? [];
	            group.push(wire);
	            groups.set(root, group);
	        }
	        return Array.from(groups.values());
	    }
	    function wiresTouch(a, b) {
	        return (pointOnWire(a.startX, a.startY, b) ||
	            pointOnWire(a.endX, a.endY, b) ||
	            pointOnWire(b.startX, b.startY, a) ||
	            pointOnWire(b.endX, b.endY, a));
	    }
	    function findNeighbouringAliasOwner(alias, wires, precedingWireByOffset) {
	        const precedingWireDbId = precedingWireByOffset.get(alias.offset);
	        if (precedingWireDbId === undefined) {
	            return undefined;
	        }
	        const ranked = wires
	            .map((wire) => ({ wire, distance: distanceToWire(alias.locX, alias.locY, wire) }))
	            .sort((left, right) => left.distance - right.distance);
	        const nearest = ranked[0];
	        if (!nearest ||
	            ranked.some((candidate) => candidate.wire !== nearest.wire && candidate.distance === nearest.distance)) {
	            return undefined;
	        }
	        return nearest.wire.dbId === precedingWireDbId ? nearest.wire : undefined;
	    }
	    function buildPrecedingWireIndex(objects) {
	        const precedingWireByOffset = new Map();
	        let precedingWireDbId;
	        for (const object of [...objects].sort((left, right) => left.offset - right.offset)) {
	            if (precedingWireDbId !== undefined) {
	                precedingWireByOffset.set(object.offset, precedingWireDbId);
	            }
	            if (isWire(object)) {
	                precedingWireDbId = object.dbId;
	            }
	        }
	        return precedingWireByOffset;
	    }
	    function findExactAliasOwner(alias, wires) {
	        const owners = wires.filter((wire) => pointOnWire(alias.locX, alias.locY, wire));
	        if (owners.length <= 1) {
	            return owners[0];
	        }
	        return owners
	            .map((wire) => ({
	            wire,
	            isPrecedingRecord: wire.offset <= alias.offset,
	            offsetDistance: Math.abs(alias.offset - wire.offset),
	            isEndpoint: isWireEndpoint(wire, alias.locX, alias.locY),
	        }))
	            .sort((left, right) => {
	            if (left.isPrecedingRecord !== right.isPrecedingRecord) {
	                return left.isPrecedingRecord ? -1 : 1;
	            }
	            if (left.offsetDistance !== right.offsetDistance) {
	                return left.offsetDistance - right.offsetDistance;
	            }
	            if (left.isEndpoint !== right.isEndpoint) {
	                return left.isEndpoint ? 1 : -1;
	            }
	            return left.wire.dbId - right.wire.dbId;
	        })[0]?.wire;
	    }
	    function findExactPointOwner(x, y, wires) {
	        return wires.find((wire) => pointOnWire(x, y, wire));
	    }
	    function findExactPointOwners(x, y, wires) {
	        return wires.filter((wire) => pointOnWire(x, y, wire));
	    }
	    function pointOnWire(x, y, wire) {
	        const cross = (x - wire.startX) * (wire.endY - wire.startY) - (y - wire.startY) * (wire.endX - wire.startX);
	        if (cross !== 0) {
	            return false;
	        }
	        return (x >= Math.min(wire.startX, wire.endX) &&
	            x <= Math.max(wire.startX, wire.endX) &&
	            y >= Math.min(wire.startY, wire.endY) &&
	            y <= Math.max(wire.startY, wire.endY));
	    }
	    function isWireEndpoint(wire, x, y) {
	        return (wire.startX === x && wire.startY === y) || (wire.endX === x && wire.endY === y);
	    }
	    function distanceToWire(x, y, wire) {
	        const dx = wire.endX - wire.startX;
	        const dy = wire.endY - wire.startY;
	        if (dx === 0 && dy === 0) {
	            return squaredDistance(x, y, wire.startX, wire.startY);
	        }
	        const t = Math.max(0, Math.min(1, ((x - wire.startX) * dx + (y - wire.startY) * dy) / (dx * dx + dy * dy)));
	        return squaredDistance(x, y, wire.startX + t * dx, wire.startY + t * dy);
	    }
	    function squaredDistance(x1, y1, x2, y2) {
	        return (x1 - x2) ** 2 + (y1 - y2) ** 2;
	    }
	    function uniqueJunctions(junctions) {
	        const seen = new Set();
	        return junctions.filter((junction) => {
	            const key = coordKey(junction.LocX, junction.LocY);
	            if (seen.has(key)) {
	                return false;
	            }
	            seen.add(key);
	            return true;
	        });
	    }
	    function coordKey(x, y) {
	        return `${x},${y}`;
	    }
	    function firstNetName(aliases) {
	        return aliases.find((alias) => alias.name)?.name ?? '';
	    }
	    function netGroupKey(name) {
	        return name.trim().toUpperCase();
	    }
	    function maxDbId(pages) {
	        let max = 0;
	        for (const page of pages) {
	            const ids = [
	                ...(page.PartInst ?? []).map((item) => item.DbId),
	                ...(page.DrawnInst ?? []).map((item) => item.DbId),
	                ...(page.Global ?? []).map((item) => item.DbId),
	                ...(page.OffPageConnector ?? []).map((item) => item.DbId),
	                ...(page.TitleBlock ?? []).map((item) => item.DbId),
	                ...(page.Port ?? []).map((item) => item.DbId),
	                ...(page.NetScalar ?? []).flatMap((net) => net.WireScalars?.map((wire) => wire.DbId) ?? []),
	                ...(page.NetBus ?? []).flatMap((net) => net.WireBus?.map((wire) => wire.DbId) ?? []),
	            ];
	            for (const id of ids) {
	                max = Math.max(max, id);
	            }
	        }
	        return max;
	    }
	    function maxPageModifyEpoch(pages) {
	        return Math.max(0, ...pages
	            .map((page) => page.parsed.pageDateTime?.modifyDateEpoch ?? 0)
	            .filter((epoch) => isRepresentableEpoch(epoch)));
	    }
	    function isRepresentableEpoch(epoch) {
	        if (epoch <= 0) {
	            return false;
	        }
	        const milliseconds = epoch * 1000;
	        return Number.isFinite(new Date(milliseconds).getTime()) && milliseconds <= LAST_FOUR_DIGIT_YEAR_MS;
	    }
	    function dbIdFromOffset(offset, namespace) {
	        return namespace * 1_000_000 + offset;
	    }
	    function pushValue(target, key, value) {
	        const arr = target[key] ?? [];
	        arr.push(value);
	        target[key] = arr;
	    }
	    function arraysEqual(left, right) {
	        return left.length === right.length && left.every((value, index) => value === right[index]);
	    }
	    function readUInt32LE(bytes, offset) {
	        return ((bytes[offset] ?? 0) |
	            ((bytes[offset + 1] ?? 0) << 8) |
	            ((bytes[offset + 2] ?? 0) << 16) |
	            ((bytes[offset + 3] ?? 0) << 24));
	    }
	    function writeUInt32LE(bytes, offset, value) {
	        bytes[offset] = value & 0xff;
	        bytes[offset + 1] = (value >> 8) & 0xff;
	        bytes[offset + 2] = (value >> 16) & 0xff;
	        bytes[offset + 3] = (value >> 24) & 0xff;
	    }
	    function findDesignName(document) {
	        const dsnStream = document.streams.find((stream) => stream.parsed?.kind === 'dsn-stream')?.parsed;
	        if (dsnStream?.kind !== 'dsn-stream' || typeof dsnStream.json !== 'object' || dsnStream.json === null) {
	            return undefined;
	        }
	        const json = dsnStream.json;
	        return (typeof json.name === 'string' ? json.name
	            : typeof json.Name === 'string' ? json.Name
	                : undefined);
	    }
	    function collectPageSymbolRoles(document) {
	        const roles = new Map();
	        for (const stream of document.streams) {
	            for (const object of stream.decodedPageObjects ?? []) {
	                if (object.kind === 'placed-symbol') {
	                    roles.set(object.symbolName, object.role);
	                }
	            }
	        }
	        return roles;
	    }
	    function isPlacedSymbolRole(role) {
	        return (object) => object.kind === 'placed-symbol' && object.role === role;
	    }
	    function isLogicalPagePort(object) {
	        return object.kind === 'placed-symbol' && isPlacedPortSymbol(object);
	    }
	    function isDrawnInst(object) {
	        return object.kind === 'drawn-inst';
	    }
	    function isWire(object) {
	        return object.kind === 'wire';
	    }
	    function isBusWire(wire) {
	        return wire.wireType === 21;
	    }
	    function isAlias(object) {
	        return object.kind === 'alias';
	    }
	    function isJunction(object) {
	        return object.kind === 'junction';
	    }
	    function isPortInstance(object) {
	        return object.kind === 'port-instance';
	    }
	    function isSymbolPin(record) {
	        return record.kind === 'symbol-pin';
	    }
	    function isCacheObjectGroupHeader(record) {
	        return record.kind === 'view-header' || (record.kind === 'named-record' && record.name !== 'Dynamic Left');
	    }
	    function isSymbolObjectRecord(record) {
	        return (record.kind === 'view-header' ||
	            record.kind === 'symbol-pin' ||
	            record.kind === 'display-property' ||
	            record.kind === 'part-value' ||
	            record.kind === 'pin-number-map' ||
	            record.kind === 'named-record');
	    }
	    function isSymbolDisplayProperty(record) {
	        return record.kind === 'display-property';
	    }
	    function isPartValue(record) {
	        return record.kind === 'part-value';
	    }
	    function isPinNumberMap(record) {
	        return record.kind === 'pin-number-map';
	    }
	    function firstSymbolPin(stream) {
	        return stream.decodedSymbolObjects?.find(isSymbolPin);
	    }
	    function firstSymbolDisplayProperty(stream) {
	        const property = stream.decodedSymbolObjects?.find(isSymbolDisplayProperty);
	        return property ? symbolDisplayPropertyFromCfb(property) : undefined;
	    }
	    function isPortSymbolName(name, pageSymbolRoles) {
	        return pageSymbolRoles.get(name) === 'port' || isPortConventionSymbolName(name);
	    }
	    function isPortConventionSymbolName(name) {
	        return /^PORT/i.test(name);
	    }
	    function isPlacedPortSymbol(object) {
	        return object.role === 'port' || (object.role === 'unknown' && isPortConventionSymbolName(object.symbolName));
	    }
	    function isOffPageSymbolName(name) {
	        return /^OFF(?:PAGE|_)/i.test(name);
	    }
	    function isErcSymbolName(name) {
	        return /^ERC/i.test(name);
	    }
	    function isBusName(name) {
	        return Boolean(name && /\[[^\]]+\]/.test(name));
	    }
	    function referencePrefixOfDesignator(reference) {
	        return /^([A-Za-z]+)\d+[A-Za-z0-9_-]*$/.exec(reference)?.[1] ?? '';
	    }
	    function leadingLettersOfPackageName(packageName) {
	        return /^[A-Za-z]+/.exec(packageName)?.[0] ?? '';
	    }
	    function isHierarchyOccurrenceReferenceName(name) {
	        return (name.length <= 64 &&
	            (/^[A-Za-z][A-Za-z0-9_.-]*\d[A-Za-z0-9_.-]*$/.test(name) || /^[A-Za-z]+\?$/.test(name) || /^\d+$/.test(name)));
	    }
	    function isPlausibleHierarchyRefInstId(value) {
	        return isDecodedHierarchyId(value) && value >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD;
	    }
	    const isDecodedHierarchyId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.isDecodedObjectId;
	    function unsignedHierarchyId(value) {
	        return value === undefined ? undefined : value >>> 0;
	    }
	    function isPlausibleHierarchyOccurrenceRow(string) {
	        const refInstId = unsignedHierarchyId(string.precedingInt32Le16);
	        if (refInstId === undefined) {
	            return false;
	        }
	        if (isPlausibleHierarchyRefInstId(refInstId)) {
	            return true;
	        }
	        return (refInstId > 0 &&
	            refInstId <= 100_000 &&
	            isPlausibleHierarchyOccurrenceId(unsignedHierarchyId(string.precedingInt32Le20)) &&
	            string.followingUInt32Le === 0);
	    }
	    function isPlausibleHierarchyOccurrenceId(value) {
	        return isDecodedHierarchyId(value) && value >= OrcadDsnCfbOccurrenceOwnership_1.HIERARCHY_ROW_ID_THRESHOLD;
	    }
	    function pkgNameFromGraphicName(graphicName) {
	        return graphicName.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
	    }
	    function stripNormalViewSuffix(value) {
	        return value.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
	    }
	    const NORMAL_VIEW_SUFFIX = '.Normal';
	    function decodedViewSuffix(name) {
	        return /\.(?:Normal|IEEE|Convert)$/i.exec(name ?? '')?.[0];
	    }
	    function isNumericString(value) {
	        return Boolean(value && /^[0-9]+$/.test(value));
	    }
	    function isCacheCatalogMarkerToken(value) {
	        return Boolean(value && ((value.length <= 2 && /^[^A-Za-z0-9]+$/.test(value)) || /^[A-Za-z]$/.test(value)));
	    }
	    function isMultipartSectionMarker(value) {
	        return Boolean(value && (/^-\d+$/.test(value) || /^[A-Z]$/.test(value)));
	    }
	    function isSourceLibraryPath(value) {
	        return Boolean(value && /\.(?:OLB|DSN)$/i.test(value));
	    }
	    function isDsnSourcePath(value) {
	        return Boolean(value && /\.DSN$/i.test(value));
	    }
	    function isPlausibleCatalogPackageName(value) {
	        if (value === 'R') {
	            return true;
	        }
	        return ((value.length > 1 || value === 'R') &&
	            /[A-Za-z0-9]/.test(value) &&
	            !isSourceLibraryPath(value) &&
	            !isNumericString(value) &&
	            !value.startsWith('$') &&
	            !/^(?:A|B|C|D|E|F|G|H|J|K|L|M|N|P|R|U|X|Y|Z|\+|-|OUT|V\+|V-|GND|VCC|Cadence|Title|Sheet|Rev|Size|Date:|of)$/i.test(value));
	    }
	    function isReferencePrefix(value) {
	        return Boolean(value && /^[A-Z]{1,4}$/.test(value));
	    }
	    function isSheetReferencePrefix(value) {
	        return Boolean(value && /^[A-Za-z][A-Za-z0-9_]{0,31}$/.test(value) && !isSourceLibraryPath(value));
	    }
	    function isDeviceDesignatorToken(value) {
	        return /^[A-Z]{1,2}$/.test(value) || /^-\d+$/.test(value);
	    }
	    function isSectionOfCatalogPackage(candidate, packageName) {
	        if (candidate === packageName) {
	            return true;
	        }
	        if (candidate.startsWith(`${packageName}-`) || candidate.startsWith(`${packageName}_`)) {
	            return true;
	        }
	        if (candidate.startsWith(packageName) && /^[A-Z]$/.test(candidate.slice(packageName.length))) {
	            return true;
	        }
	        return false;
	    }
	    function isAlternatePackageCatalogSection(candidate, packageName) {
	        return candidate !== packageName && candidate.startsWith(`${packageName}_`) && /_M\d+M\d+$/i.test(candidate);
	    }
	})(OrcadDsnCfbLogicalSchemaConverter$1 || (OrcadDsnCfbLogicalSchemaConverter.OrcadDsnCfbLogicalSchemaConverter = OrcadDsnCfbLogicalSchemaConverter$1 = {}));
	function convertOrCadDsnCfbToLogicalSchema(document, options = {}) {
	    return OrcadDsnCfbLogicalSchemaConverter$1.convertDocument(document, options);
	}
	function parseOrCadDsnCfbLogicalSchema(fileContents, options = {}) {
	    return OrcadDsnCfbLogicalSchemaConverter$1.parseLogicalSchema(fileContents, options);
	}
	
	return OrcadDsnCfbLogicalSchemaConverter;
}

var hasRequiredDsncfb;

function requireDsncfb () {
	if (hasRequiredDsncfb) return dsncfb;
	hasRequiredDsncfb = 1;
	(function (exports) {
		var __createBinding = (dsncfb && dsncfb.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (dsncfb && dsncfb.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireDsnCfbBinarySpec(), exports);
		__exportStar(requireOrcadDsnCfbDeclarativeSpecs(), exports);
		__exportStar(requireOrcadDsnCfbLogicalSchemaConverter(), exports);
		__exportStar(requireOrcadDsnCfbParser(), exports);
		__exportStar(requireOrcadDsnCfbTypes(), exports);
		
	} (dsncfb));
	return dsncfb;
}

var hasRequiredOrcadValidation;

function requireOrcadValidation () {
	if (hasRequiredOrcadValidation) return orcadValidation;
	hasRequiredOrcadValidation = 1;
	Object.defineProperty(orcadValidation, "__esModule", { value: true });
	orcadValidation.OrCADXProjectMetadataKey = orcadValidation.OrCADXFileType = void 0;
	orcadValidation.getOrCADXFileType = getOrCADXFileType;
	orcadValidation.validateOrCADXFile = validateOrCADXFile;
	orcadValidation.detectOrcadProjects = detectOrcadProjects;
	const sigma_engine_shared_1 = requireDist$1();
	const project_detection_1 = requireProjectDetection();
	const opj_1 = requireOpj();
	const ONLParser_1 = requireONLParser();
	const xmlutil_1 = requireXmlutil();
	const LogicalSchemaClasses_1 = requireLogicalSchemaClasses();
	const dsncfb_1 = requireDsncfb();
	const OrcadDsnCfbLogicalSchemaConverter_1 = requireOrcadDsnCfbLogicalSchemaConverter();
	var OrCADXFileType;
	(function (OrCADXFileType) {
	    OrCADXFileType["OrCADCaptureProject"] = ".opj";
	    OrCADXFileType["OrCADCaptureSchematic"] = ".dsn";
	    OrCADXFileType["OrCADCaptureSchematicXml"] = ".xml";
	    OrCADXFileType["OrCADCaptureInternalNetlist"] = ".onl";
	})(OrCADXFileType || (orcadValidation.OrCADXFileType = OrCADXFileType = {}));
	var OrCADXProjectMetadataKey;
	(function (OrCADXProjectMetadataKey) {
	    OrCADXProjectMetadataKey["FileType"] = "file-type";
	    OrCADXProjectMetadataKey["FileVersion"] = "file-version";
	    OrCADXProjectMetadataKey["LogicalSchema"] = "logical-schema";
	    OrCADXProjectMetadataKey["LogicalSchemaDiagnostics"] = "logical-schema-diagnostics";
	    OrCADXProjectMetadataKey["ONLNetlist"] = "onl-netlist";
	})(OrCADXProjectMetadataKey || (orcadValidation.OrCADXProjectMetadataKey = OrCADXProjectMetadataKey = {}));
	const XML_SNIFF_HEAD_CHARS = 4096;
	function looksLikeOrcadDsnXml(fileContents) {
	    const head = typeof fileContents === 'string' ?
	        fileContents.slice(0, XML_SNIFF_HEAD_CHARS)
	        : new TextDecoder().decode(fileContents.subarray(0, XML_SNIFF_HEAD_CHARS));
	    const lower = head.toLowerCase();
	    return /<design[\s>]/.test(lower) && lower.includes('dsn.xsd');
	}
	function getOrCADXFileType(filename, fileContents) {
	    const path = filename.split('/');
	    if (path.length < 1) {
	        return false;
	    }
	    const parts = path[path.length - 1].split('.');
	    if (parts.length < 1) {
	        return false;
	    }
	    let ext = parts[parts.length - 1].toLowerCase();
	    if (parts.length >= 1) {
	        ext = '.' + ext;
	    }
	    switch (ext.toLowerCase().trim()) {
	        case OrCADXFileType.OrCADCaptureProject:
	            return OrCADXFileType.OrCADCaptureProject;
	        case OrCADXFileType.OrCADCaptureSchematic:
	            return OrCADXFileType.OrCADCaptureSchematic;
	        case OrCADXFileType.OrCADCaptureSchematicXml:
	            if (fileContents !== undefined && !looksLikeOrcadDsnXml(fileContents)) {
	                return false;
	            }
	            return OrCADXFileType.OrCADCaptureSchematicXml;
	        case OrCADXFileType.OrCADCaptureInternalNetlist:
	            return OrCADXFileType.OrCADCaptureInternalNetlist;
	    }
	    return false;
	}
	function orcadSemanticType(type) {
	    switch (type) {
	        case OrCADXFileType.OrCADCaptureProject:
	            return 'project';
	        case OrCADXFileType.OrCADCaptureSchematic:
	        case OrCADXFileType.OrCADCaptureSchematicXml:
	            return 'schematic';
	        case OrCADXFileType.OrCADCaptureInternalNetlist:
	            return 'netlist';
	        default:
	            return 'other';
	    }
	}
	async function validateOrCADXFile(filename, fileContents) {
	    const type = getOrCADXFileType(filename, fileContents);
	    if (!type) {
	        return {
	            isValid: false,
	            errors: [`Invalid Cadence OrCAD X file or contents ${filename}`],
	        };
	    }
	    const semanticType = orcadSemanticType(type);
	    let validationResult;
	    try {
	        switch (type) {
	            case OrCADXFileType.OrCADCaptureProject:
	                validationResult = await validateOrCADXProjectFile(filename, fileContentsToString(fileContents));
	                break;
	            case OrCADXFileType.OrCADCaptureSchematic:
	                validationResult = await validateOrCADXSchematicDSNFile(filename, fileContents);
	                break;
	            case OrCADXFileType.OrCADCaptureSchematicXml:
	                validationResult = await validateOrCADXSchematicXMLFile(filename, fileContentsToString(fileContents));
	                break;
	            case OrCADXFileType.OrCADCaptureInternalNetlist:
	                validationResult = await validateOrCADXInternalNetlistFile(filename, fileContentsToString(fileContents));
	                break;
	            default:
	                return {
	                    isValid: true,
	                    data: {
	                        semanticType,
	                        fileFormat: {
	                            cadTool: 'orcad',
	                            type: type,
	                        },
	                        referencedFiles: [],
	                        metadata: {},
	                    },
	                };
	        }
	    }
	    catch (e) {
	        return {
	            isValid: false,
	            semanticType,
	            errors: [`Invalid Cadence OrCAD X contents ${filename}`, e instanceof Error ? e.message : String(e)],
	            messageResults: e instanceof Error ? [(0, sigma_engine_shared_1.errorToMessageResult)(e)] : undefined,
	        };
	    }
	    if (!validationResult) {
	        return {
	            isValid: false,
	            semanticType,
	            errors: [`Invalid Cadence OrCAD X contents ${filename}`],
	        };
	    }
	    return {
	        isValid: true,
	        data: {
	            semanticType: semanticType,
	            fileFormat: {
	                cadTool: 'orcad',
	                type: type,
	                version: validationResult.version,
	            },
	            referencedFiles: validationResult.referencedFiles,
	            metadata: validationResult.metadata,
	        },
	    };
	}
	function fileContentsToBytes(fileContents) {
	    if (fileContents instanceof Uint8Array) {
	        return fileContents;
	    }
	    return Uint8Array.from(Array.from(fileContents).map((char) => char.charCodeAt(0) & 0xff));
	}
	function fileContentsToString(fileContents) {
	    if (typeof fileContents === 'string') {
	        return fileContents;
	    }
	    return new TextDecoder().decode(fileContents);
	}
	function validateOrCADXSchematicDSNFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureSchematic)) {
	        return undefined;
	    }
	    const diagnostics = [];
	    const design = (0, dsncfb_1.parseOrCadDsnCfbLogicalSchema)(fileContentsToBytes(fileContents), {
	        sourceFileName: filename,
	        diagnostics,
	    });
	    const referencedFiles = collectLiveReferencedFiles(design);
	    const diagnosticInnerResults = diagnostics.map((diagnostic) => ({
	        type: diagnostic.type ?? 'orcad-parsing',
	        message: diagnostic.message,
	        severity: BOUNDARY_LEVEL_BY_DIAGNOSTIC_SEVERITY[diagnostic.severity],
	    }));
	    return {
	        version: undefined,
	        referencedFiles,
	        warnings: diagnostics.length > 0 ?
	            {
	                type: 'orcad-parsing',
	                message: `OrCAD DSN CFB parsing completed with ${diagnostics.length} diagnostic(s)`,
	                severity: diagnosticInnerResults.reduce((highest, result) => (0, sigma_engine_shared_1.getHighestBoundaryLevel)(highest, result.severity ?? sigma_engine_shared_1.BoundaryLevel.Recommendation), sigma_engine_shared_1.BoundaryLevel.Recommendation),
	                innerResults: diagnosticInnerResults,
	            }
	            : {
	                type: 'orcad-parsing',
	                message: 'OrCAD DSN CFB parsing completed',
	                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                innerResults: [],
	            },
	        metadata: {
	            [OrCADXProjectMetadataKey.LogicalSchema]: design,
	            [OrCADXProjectMetadataKey.LogicalSchemaDiagnostics]: diagnosticInnerResults,
	        },
	    };
	}
	const BOUNDARY_LEVEL_BY_DIAGNOSTIC_SEVERITY = {
	    [OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity.Info]: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	    [OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity.Warning]: sigma_engine_shared_1.BoundaryLevel.Warning,
	    [OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity.Error]: sigma_engine_shared_1.BoundaryLevel.Error,
	};
	function collectLiveReferencedFiles(design) {
	    const referencedFiles = new Set();
	    for (const schematic of design.Schematic) {
	        for (const page of schematic.Page) {
	            for (const inst of page.DrawnInst ?? []) {
	                if (inst.ContentsLibName) {
	                    referencedFiles.add(inst.ContentsLibName.replace(/\\/g, '/').replace(/^\.\//, ''));
	                }
	            }
	            for (const inst of page.PartInst ?? []) {
	                if (inst.ContentsLibName) {
	                    referencedFiles.add(inst.ContentsLibName.replace(/\\/g, '/').replace(/^\.\//, ''));
	                }
	            }
	        }
	    }
	    return Array.from(referencedFiles);
	}
	function validateOrCADXProjectFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureProject)) {
	        return undefined;
	    }
	    try {
	        const result = (0, opj_1.parseOrCADCaptureProject)(fileContents);
	        if (!result) {
	            return undefined;
	        }
	        const referencedFiles = new Set();
	        for (const path of result.project.globalState?.fileView?.paths ?? []) {
	            if (path.fsPath) {
	                referencedFiles.add(path.fsPath);
	            }
	        }
	        return {
	            referencedFiles: Array.from(referencedFiles),
	            metadata: {
	                [OrCADXProjectMetadataKey.FileType]: `OrCAD Capture ${result.project.projectType ?? 'Unknown'} Project`,
	                [OrCADXProjectMetadataKey.FileVersion]: result.project.projectVersion ?? 'unknown',
	            },
	            version: result.project.projectVersion,
	            warnings: result.processingResults,
	        };
	    }
	    catch (e) {
	        return undefined;
	    }
	}
	function validateOrCADXSchematicXMLFile(filename, fileContents) {
	    const parsed = (0, xmlutil_1.ParseXml)(fileContents);
	    if (!parsed.Design?.['$xsi:noNamespaceSchemaLocation'] ||
	        !/(\\|\/)dsn\.xsd$/.test(parsed.Design['$xsi:noNamespaceSchemaLocation'])) {
	        throw new Error('Not a valid OrCAD DSN XML file, or unknown schema version');
	    }
	    const validationOpts = {
	        processingResults: {
	            type: 'orcad-parsing',
	            message: 'OrCAD DSN XML parsing completed',
	            innerResults: [],
	            severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	        },
	    };
	    const design = LogicalSchemaClasses_1.OrCadDSNClasses.Design.FromXml(parsed.Design, validationOpts);
	    const referencedFiles = collectLiveReferencedFiles(design);
	    const versionMatch = /\\orcadx_([0-9.]+)\\/.exec(parsed.Design['$xsi:noNamespaceSchemaLocation']);
	    let version;
	    if (versionMatch) {
	        version = versionMatch[1];
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(validationOpts.processingResults);
	    if (validationOpts.processingResults.severity !== sigma_engine_shared_1.BoundaryLevel.Recommendation) {
	        validationOpts.processingResults.message = `OrCAD DSN XML parsing completed with ${validationOpts.processingResults.severity}s`;
	    }
	    return {
	        version: version,
	        referencedFiles,
	        warnings: validationOpts.processingResults,
	        metadata: {
	            [OrCADXProjectMetadataKey.LogicalSchema]: design,
	        },
	    };
	}
	function detectOrcadProjects(files, fileDetails) {
	    const schematicDirStems = new Set(fileDetails
	        .filter((d) => d.cadTool === 'orcad' && d.semanticType === 'schematic')
	        .map((d) => `${(0, project_detection_1.normalizePathForCompare)((0, project_detection_1.dirnameForward)(d.relativePath))}/${(0, project_detection_1.basenameWithoutExtension)(d.relativePath).toLowerCase()}`));
	    const opjFiles = fileDetails
	        .filter((d) => d.cadTool === 'orcad' && d.relativePath.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureProject))
	        .map((d) => ({ relativePath: d.relativePath, normDir: (0, project_detection_1.normalizePathForCompare)((0, project_detection_1.dirnameForward)(d.relativePath)) }));
	    const sidecarOpjPaths = new Set(opjFiles
	        .filter((opj) => {
	        const stem = (0, project_detection_1.basenameWithoutExtension)(opj.relativePath).toLowerCase();
	        if (!schematicDirStems.has(`${opj.normDir}/${stem}`)) {
	            return false;
	        }
	        return opjFiles.some((other) => other.relativePath !== opj.relativePath &&
	            (0, project_detection_1.isSameOrDescendantDir)(opj.normDir, other.normDir) &&
	            opj.normDir !== other.normDir);
	    })
	        .map((opj) => opj.relativePath));
	    const isProjectAnchorOpj = (relativePath) => {
	        if (!relativePath.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureProject)) {
	            return false;
	        }
	        return !sidecarOpjPaths.has(relativePath);
	    };
	    return (0, project_detection_1.detectAnchoredProjects)({
	        tool: 'orcad',
	        toolDisplayName: 'OrCAD',
	        files,
	        fileDetails,
	        primaryAnchorMatch: isProjectAnchorOpj,
	        primaryAnchorLabel: OrCADXFileType.OrCADCaptureProject,
	    });
	}
	function validateOrCADXInternalNetlistFile(filename, fileContents) {
	    if (!filename.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureInternalNetlist)) {
	        return undefined;
	    }
	    try {
	        const result = (0, ONLParser_1.parseOrCADNetlist)(fileContents);
	        if (!result.netlist) {
	            return undefined;
	        }
	        const version = result.netlist.description?.program?.version;
	        return {
	            referencedFiles: [],
	            metadata: {
	                [OrCADXProjectMetadataKey.FileType]: 'OrCAD Capture Netlist',
	                [OrCADXProjectMetadataKey.FileVersion]: version ?? 'unknown',
	                [OrCADXProjectMetadataKey.ONLNetlist]: result.netlist,
	            },
	            version,
	            warnings: result.processingResults,
	        };
	    }
	    catch {
	        return undefined;
	    }
	}
	
	return orcadValidation;
}

var orcadRequiredFiles = {};

var hasRequiredOrcadRequiredFiles;

function requireOrcadRequiredFiles () {
	if (hasRequiredOrcadRequiredFiles) return orcadRequiredFiles;
	hasRequiredOrcadRequiredFiles = 1;
	Object.defineProperty(orcadRequiredFiles, "__esModule", { value: true });
	orcadRequiredFiles.checkOrcadRequiredFiles = checkOrcadRequiredFiles;
	const sigma_engine_shared_1 = requireDist$1();
	const orcadValidation_1 = requireOrcadValidation();
	function getParsedDesign(detail) {
	    if (detail.cadTool !== 'orcad' || detail.semanticType !== 'schematic' || !detail.fileValidationResult?.isValid) {
	        return undefined;
	    }
	    const value = detail.fileValidationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
	    if (typeof value !== 'object' || value === null || !Array.isArray(value.Schematic)) {
	        return undefined;
	    }
	    return value;
	}
	function designIsHierarchical(design) {
	    for (const schematic of design.Schematic) {
	        for (const page of schematic.Page ?? []) {
	            const drawnRef = (page.DrawnInst ?? []).some((inst) => inst.ContentsLibName !== '' || inst.ContentsViewName !== '');
	            const partRef = (page.PartInst ?? []).some((inst) => inst.ContentsViewType === 1);
	            if (drawnRef || partRef) {
	                return true;
	            }
	        }
	    }
	    return false;
	}
	function checkBinaryDsnRule(context) {
	    const dsnExt = orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic.toLowerCase();
	    if (context.files.some((f) => f.relativePath.toLowerCase().endsWith(dsnExt))) {
	        return [];
	    }
	    for (const detail of context.fileDetails) {
	        if (!detail.relativePath.toLowerCase().endsWith(orcadValidation_1.OrCADXFileType.OrCADCaptureSchematicXml)) {
	            continue;
	        }
	        const design = getParsedDesign(detail);
	        if (!design || !designIsHierarchical(design)) {
	            continue;
	        }
	        return [
	            {
	                type: 'required-file-check',
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                message: `"${detail.relativePath}" is a hierarchical design provided as a .xml export; upload the original .dsn to preserve exact net naming`,
	                rule: 'orcad-binary-dsn',
	                cadTool: 'orcad',
	                requiredFileExt: orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic,
	                triggeringFile: detail.relativePath,
	                errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.MissingRequiredFile,
	            },
	        ];
	    }
	    return [];
	}
	function checkOrcadRequiredFiles(context) {
	    return checkBinaryDsnRule(context);
	}
	
	return orcadRequiredFiles;
}

var parseOrCADProject = {};

var mergeDisjointComponentsUtil = {};

var hasRequiredMergeDisjointComponentsUtil;

function requireMergeDisjointComponentsUtil () {
	if (hasRequiredMergeDisjointComponentsUtil) return mergeDisjointComponentsUtil;
	hasRequiredMergeDisjointComponentsUtil = 1;
	Object.defineProperty(mergeDisjointComponentsUtil, "__esModule", { value: true });
	mergeDisjointComponentsUtil.mergeDisjointComponents = mergeDisjointComponents;
	mergeDisjointComponentsUtil.createMergedComponent = createMergedComponent;
	const _ = requireLodash();
	const sigma_engine_shared_1 = requireDist$1();
	function getDefaultOpts() {
	    return {
	        mergeSignalsWithSamePinRef: true,
	    };
	}
	function mergeDisjointComponents(project, inputOpts = {}) {
	    const opts = { ...getDefaultOpts(), ...inputOpts };
	    const disjointComponents = new Map();
	    const componentsModules = new Map();
	    for (const m of project.modules) {
	        const symbolComponentPairs = new Set();
	        let isDisjoint = false;
	        for (const instance of m.instances) {
	            const design = project.designs[instance.designId];
	            if (design.modelType !== 'schematic') {
	                continue;
	            }
	            const module = design.modules.find((m) => m.ref === instance.ref);
	            if (!module) {
	                throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
	            }
	            if (!componentsModules.has(module.componentRef)) {
	                componentsModules.set(module.componentRef, [m]);
	            }
	            else {
	                componentsModules.get(module.componentRef).push(m);
	            }
	            const identifier = getDisjointComponentUid(module);
	            symbolComponentPairs.add(identifier);
	            if (module.componentRef !== m.componentRef) {
	                isDisjoint = true;
	            }
	        }
	        if (isDisjoint) {
	            const combined = Array.from(symbolComponentPairs.values()).sort().join(',');
	            if (!disjointComponents.has(combined)) {
	                disjointComponents.set(combined, [m]);
	            }
	            else {
	                disjointComponents.get(combined).push(m);
	            }
	        }
	    }
	    for (const [key, modules] of disjointComponents) {
	        const disjointComponentMessageResult = {
	            type: 'disjoint-component-merge',
	            message: '',
	            innerResults: [],
	        };
	        const newComponent = createMergedComponent(modules, project, opts, disjointComponentMessageResult);
	        disjointComponentMessageResult.message = `Merged disjoint components into ${newComponent.newRef}`;
	        disjointComponentMessageResult.newComponentRef = newComponent.newRef;
	        for (const m of modules) {
	            m.componentRef = newComponent.newRef;
	            for (const instance of m.instances) {
	                const design = project.designs[instance.designId];
	                if (design.modelType === 'schematic') {
	                    updateSchematicModule(design, instance, project, newComponent, disjointComponentMessageResult);
	                }
	                else if (design.modelType === 'pcb') {
	                    updatePcbModule(design, instance, project, newComponent);
	                }
	            }
	        }
	        project.processingResults.innerResults.push(disjointComponentMessageResult);
	        disjointComponentMessageResult.pinMapping = sigma_engine_shared_1.Utils.deepConvertMaps(newComponent.symbolPinMapping);
	    }
	    for (const [component, modules] of componentsModules) {
	        if (modules.every((m) => m.componentRef !== component)) {
	            delete project.components[component];
	        }
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
	    return project;
	}
	function updateSchematicModule(design, instance, project, newComponent, messageResult) {
	    const module = design.modules.find((m) => m.ref === instance.ref);
	    if (!module) {
	        throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
	    }
	    const oldComponent = project.components[module.componentRef];
	    if (!oldComponent) {
	        throw new Error(`Component ${module.componentRef} not found in project`);
	    }
	    const oldSymbol = oldComponent.symbols.find((s) => s.ref === module.footprintRef);
	    if (!oldSymbol) {
	        throw new Error(`Symbol ${module.footprintRef} not found in component ${oldComponent.name}`);
	    }
	    const newSymbolMapping = newComponent.symbolPinMapping.get(`${module.componentRef}::${module.footprintRef}`);
	    if (!newSymbolMapping) {
	        throw new Error(`New symbol not found for ${module.footprintRef}`);
	    }
	    module.footprintRef = newSymbolMapping.newSymbolRef;
	    module.componentRef = newComponent.newRef;
	    const oldPinNets = module.pinNets;
	    module.pinNets = {};
	    const updateMessageResult = {
	        type: 'schematic-module-updated',
	        message: `Updated schematic module ${module.ref} (${module.name})`,
	        moduleRef: module.ref,
	        oldComponentRef: oldComponent.name,
	        newComponentRef: newComponent.newRef,
	        oldSymbolRef: oldSymbol.ref,
	        newSymbolRef: newSymbolMapping.newSymbolRef,
	        innerResults: [],
	    };
	    for (const [oldPinRef, newPinRef] of newSymbolMapping.pinMapping) {
	        const oldNet = oldPinNets[oldPinRef]?.valueOf();
	        const designNet = design.nets[oldNet];
	        if (designNet === undefined) {
	            if (oldNet !== undefined) {
	                updateMessageResult.severity = sigma_engine_shared_1.DesignModels.BoundaryLevel.Error;
	                updateMessageResult.innerResults?.push({
	                    type: 'net-not-found',
	                    message: `Net ${oldNet} not found in design ${instance.designId}`,
	                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
	                    netName: oldNet,
	                });
	            }
	            continue;
	        }
	        module.pinNets[newPinRef] = oldPinNets[oldPinRef];
	        for (const connection of designNet.connections) {
	            if (connection.moduleRef === module.ref && connection.pinRef === oldPinRef) {
	                connection.pinRef = newPinRef;
	            }
	        }
	    }
	    if (updateMessageResult.innerResults?.length === 0) {
	        delete updateMessageResult.innerResults;
	    }
	    messageResult.innerResults.push(updateMessageResult);
	}
	function getDisjointComponentUid(module) {
	    return `\0${module.componentRef}::${module.footprintRef}\0`;
	}
	function createMergedComponent(modules, project, opts, messageResult) {
	    const moduleComponentRefs = Array.from(new Set(modules.map((m) => m.componentRef)));
	    messageResult.mergedComponentRefs = moduleComponentRefs;
	    const pcbModules = modules
	        .flatMap((m) => m.instances.flatMap((i) => {
	        const design = project.designs[i.designId];
	        if (design.modelType !== 'pcb') {
	            return null;
	        }
	        return design.modules.find((m2) => m2.ref === i.ref);
	    }))
	        .filter((m) => m !== null && m !== undefined);
	    const footprintNames = new Set(pcbModules.map((m) => m.footprintRef));
	    if (footprintNames.size > 1) {
	        throw new Error('Cannot merge modules with different footprint names');
	    }
	    let cpNameSuffix = '';
	    const cpBaseName = `Merged_${moduleComponentRefs.join('::')}`;
	    while (`${cpBaseName}${cpNameSuffix}` in project.components) {
	        cpNameSuffix = sigma_engine_shared_1.Utils.randomAlphaStr(4);
	    }
	    const newCp = {
	        name: `${cpBaseName}${cpNameSuffix}`,
	        numPins: 0,
	        numPads: 0,
	        footprints: [],
	        symbols: [],
	        signals: [],
	        metadata: {},
	    };
	    const result = {
	        newRef: newCp.name,
	        component: newCp,
	        footprintName: pcbModules[0]?.footprintRef,
	        symbolPinMapping: new Map(),
	    };
	    let useFootprint;
	    if (pcbModules.length > 0) {
	        const firstModule = pcbModules[0];
	        useFootprint = project.components[firstModule.componentRef]?.footprints.find((f) => f.ref === firstModule.footprintRef);
	        if (useFootprint) {
	            newCp.footprints.push(useFootprint);
	        }
	    }
	    for (const m of modules) {
	        for (const instance of m.instances) {
	            const design = project.designs[instance.designId];
	            if (design?.modelType !== 'schematic') {
	                continue;
	            }
	            const designModule = design.modules.find((m2) => m2.ref === instance.ref);
	            if (!designModule) {
	                throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
	            }
	            const oldComponent = project.components[designModule.componentRef];
	            if (!oldComponent) {
	                throw new Error(`Component ${designModule.componentRef} not found in project`);
	            }
	            const oldSymbol = oldComponent.symbols.find((s) => s.ref === designModule.footprintRef);
	            if (!oldSymbol) {
	                throw new Error(`Symbol ${designModule.footprintRef} not found in component ${oldComponent.name}`);
	            }
	            const combinedRef = `${designModule.componentRef}::${designModule.footprintRef}`;
	            if (result.symbolPinMapping.has(combinedRef)) {
	                continue;
	            }
	            if (oldComponent.model) {
	                if (newCp.model) {
	                    if (!_.isEqual(newCp.model, oldComponent.model)) {
	                        throw new Error(`Cannot merge components with different sigma models: ${newCp.model.modelId} and ${oldComponent.model.modelId}`);
	                    }
	                }
	                else {
	                    newCp.model = structuredClone(oldComponent.model);
	                }
	            }
	            let suffix = '';
	            while (newCp.symbols.some((s) => s.ref === `${oldSymbol.ref}${suffix}`)) {
	                suffix = sigma_engine_shared_1.Utils.randomAlphaStr(3);
	            }
	            const newMapping = {
	                newSymbolRef: `${designModule.footprintRef}${suffix}`,
	                pinMapping: new Map(),
	            };
	            result.symbolPinMapping.set(combinedRef, newMapping);
	            for (const signal of oldComponent.signals) {
	                if (!signal.pinRefs.some((pinRef) => pinRef !== null && oldSymbol.pins.some((p) => p.ref === pinRef))) {
	                    continue;
	                }
	                let sid = signal.id;
	                while (newCp.signals.some((s) => s.id === sid)) {
	                    sid = `${signal.id}_${sigma_engine_shared_1.Utils.randomAlphaStr(3)}`;
	                }
	                const newPinRefs = signal.pinRefs
	                    .filter((pinRef) => oldSymbol.pins.some((p) => p.ref === pinRef))
	                    .map((oldSignalPinRef) => {
	                    if (opts.mergeSignalsWithSamePinRef) {
	                        return [oldSignalPinRef, oldSignalPinRef];
	                    }
	                    let suffix = '';
	                    while (newCp.symbols.some((s) => s.pins.some((p) => p.ref === `${oldSignalPinRef}${suffix}`))) {
	                        suffix = sigma_engine_shared_1.Utils.randomAlphaStr(3);
	                    }
	                    return [oldSignalPinRef, `${oldSignalPinRef}${suffix}`];
	                });
	                const matchingExistingSignals = newCp.signals.filter((s) => s.pinRefs.some((p) => newPinRefs.some(([, newP]) => p === newP)));
	                if (matchingExistingSignals.length > 1) {
	                    throw new Error(`Cannot merge signals with same pin refs: ${matchingExistingSignals.map((s) => s.id).join(', ')}`);
	                }
	                else if (matchingExistingSignals.length === 1) {
	                    const existing = matchingExistingSignals[0];
	                    existing.pinRefs = Array.from(new Set([...existing.pinRefs, ...newPinRefs.map(([, newP]) => newP)]));
	                    existing.padRefs = Array.from(new Set([...existing.padRefs, ...signal.padRefs]));
	                }
	                else {
	                    newCp.signals.push({
	                        ...signal,
	                        id: sid,
	                        pinRefs: newPinRefs.map(([, pr]) => pr),
	                        padRefs: useFootprint ? signal.padRefs.filter((padRef) => useFootprint.pads.some((p) => p.ref === padRef)) : [],
	                    });
	                }
	                for (const [oldPin, newPin] of newPinRefs) {
	                    if (oldPin === null) {
	                        continue;
	                    }
	                    if (newPin === null) {
	                        throw new Error('New pin is null');
	                    }
	                    newMapping.pinMapping.set(oldPin, newPin);
	                }
	            }
	            newCp.symbols.push({
	                ...oldSymbol,
	                ref: newMapping.newSymbolRef,
	                pins: oldSymbol.pins.map((p) => {
	                    const newPinRef = newMapping.pinMapping.get(p.ref) ?? p.ref;
	                    return { ...p, ref: newPinRef };
	                }),
	            });
	        }
	    }
	    newCp.numPads = newCp.footprints.reduce((p, c) => p + c.pads.length, 0);
	    newCp.numPins = newCp.symbols.reduce((p, c) => p + c.pins.length, 0);
	    project.components[newCp.name] = newCp;
	    return result;
	}
	function updatePcbModule(design, instance, project, newComponent) {
	    if (!newComponent.footprintName) {
	        throw new Error('No footprint name');
	    }
	    const module = design.modules.find((m) => m.ref === instance.ref);
	    if (!module) {
	        throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
	    }
	    module.componentRef = newComponent.newRef;
	    module.footprintRef = newComponent.footprintName;
	}
	
	return mergeDisjointComponentsUtil;
}

var OrcadDsnOrchestratedTransformer = {};

var TransformOrchestrator = {};

var TransformObjectRegistry = {};

var hasRequiredTransformObjectRegistry;

function requireTransformObjectRegistry () {
	if (hasRequiredTransformObjectRegistry) return TransformObjectRegistry;
	hasRequiredTransformObjectRegistry = 1;
	Object.defineProperty(TransformObjectRegistry, "__esModule", { value: true });
	TransformObjectRegistry.TransformObjectRegistry = void 0;
	class TransformObjectRegistry$1 {
	    constructor() {
	        this.objects = new Set();
	        this.byKey = new Map();
	    }
	    register(obj) {
	        if (this.objects.has(obj)) {
	            throw new Error('Object already registered. Call hasObject() first to check.');
	        }
	        const key = obj.deduplicationKey;
	        this.objects.add(obj);
	        const constructor = obj.constructor;
	        let typeMap = this.byKey.get(constructor);
	        if (!typeMap) {
	            typeMap = new Map();
	            this.byKey.set(constructor, typeMap);
	        }
	        let objs = typeMap.get(key);
	        const existing = objs?.[0];
	        if (!objs) {
	            objs = [];
	            typeMap.set(key, objs);
	        }
	        objs.push(obj);
	        if (key !== undefined && existing !== undefined) {
	            obj.linkTo(existing);
	            return false;
	        }
	        return true;
	    }
	    getPrimaries(type) {
	        const typeMap = this.byKey.get(type);
	        if (!typeMap) {
	            return [];
	        }
	        let result = [];
	        for (const [key, objs] of typeMap) {
	            if (key === undefined) {
	                result = result.concat(objs);
	                continue;
	            }
	            const primary = objs[0];
	            if (primary) {
	                result.push(primary);
	            }
	        }
	        return result;
	    }
	    getAllOfType(type) {
	        const typeMap = this.byKey.get(type);
	        if (!typeMap) {
	            return [];
	        }
	        const result = [];
	        for (const [, objs] of typeMap) {
	            for (const obj of objs) {
	                result.push(obj);
	            }
	        }
	        return result;
	    }
	    hasObject(obj) {
	        return this.objects.has(obj);
	    }
	    get size() {
	        return this.objects.size;
	    }
	    getPrimaryByKey(type, key) {
	        if (key === undefined) {
	            throw new Error('getPrimaryByKey() called with undefined key');
	        }
	        const typeMap = this.byKey.get(type);
	        if (!typeMap) {
	            return undefined;
	        }
	        const objs = typeMap.get(key);
	        return objs?.[0];
	    }
	    clear() {
	        this.objects.clear();
	        this.byKey.clear();
	    }
	}
	TransformObjectRegistry.TransformObjectRegistry = TransformObjectRegistry$1;
	
	return TransformObjectRegistry;
}

var hasRequiredTransformOrchestrator;

function requireTransformOrchestrator () {
	if (hasRequiredTransformOrchestrator) return TransformOrchestrator;
	hasRequiredTransformOrchestrator = 1;
	Object.defineProperty(TransformOrchestrator, "__esModule", { value: true });
	TransformOrchestrator.TransformOrchestrator = void 0;
	const TransformObjectRegistry_1 = requireTransformObjectRegistry();
	class TransformOrchestrator$1 {
	    constructor(config) {
	        this.generators = [];
	        this.objectRegistry = new TransformObjectRegistry_1.TransformObjectRegistry();
	        this.generationQueue = [];
	        this.generatorsByRunFor = new Map();
	        for (const generator of config.generators) {
	            this.generators.push(generator);
	            if (generator.runsFor === null) {
	                const existing = this.generatorsByRunFor.get(null) ?? [];
	                existing.push(generator);
	                this.generatorsByRunFor.set(null, existing);
	            }
	            else {
	                const runsForArray = generator.runsFor;
	                for (const objectType of runsForArray) {
	                    const existing = this.generatorsByRunFor.get(objectType) ?? [];
	                    existing.push(generator);
	                    this.generatorsByRunFor.set(objectType, existing);
	                }
	            }
	        }
	    }
	    transform(baseContext, returnObjectType) {
	        for (const generator of this.generatorsByRunFor.get(null) ?? []) {
	            for (const yieldedObject of generator.generate(null, baseContext)) {
	                this.registerObject(yieldedObject);
	            }
	        }
	        this.processGenerationQueue(baseContext);
	        const primaries = this.objectRegistry.getPrimaries(returnObjectType);
	        return primaries.map((obj) => obj.compiled);
	    }
	    registerObject(object, visitedObjects = new Set()) {
	        if (this.objectRegistry.hasObject(object)) {
	            return;
	        }
	        for (const dep of object.dependencies) {
	            if (visitedObjects.has(dep)) {
	                throw new Error(`Circular dependency detected: ${[...visitedObjects, dep].map((o) => `${o.constructor.name}(${o.deduplicationKey})`).join(' → ')}`);
	            }
	            if (!this.objectRegistry.hasObject(dep)) {
	                this.registerObject(dep, new Set([...visitedObjects, dep]));
	            }
	        }
	        for (const dep of object.creates) {
	            if (visitedObjects.has(dep)) {
	                continue;
	            }
	            if (!this.objectRegistry.hasObject(dep)) {
	                this.registerObject(dep, new Set([...visitedObjects, dep]));
	            }
	        }
	        if (this.objectRegistry.hasObject(object)) {
	            return;
	        }
	        const isPrimary = this.objectRegistry.register(object);
	        if (isPrimary) {
	            this.generationQueue.push(object);
	        }
	        let current = object.parent;
	        const visited = new Set([object]);
	        while (current) {
	            if (visited.has(current)) {
	                throw new Error('Circular parent relationship detected');
	            }
	            visited.add(current);
	            if (current.canAdd(object)) {
	                if (current.isCompiled) {
	                    throw new Error(`Cannot add child ${object.constructor.name} to compiled object: ${current.constructor.name}`);
	                }
	                current.add(object);
	                break;
	            }
	            current = current.parent;
	        }
	    }
	    processGenerationQueue(rootContext) {
	        while (this.generationQueue.length > 0) {
	            const item = this.generationQueue.shift();
	            if (item === undefined) {
	                break;
	            }
	            const objectConstructor = item.constructor;
	            const matchingGenerators = this.generatorsByRunFor.get(objectConstructor) ?? [];
	            for (const generator of matchingGenerators) {
	                for (const yieldedObject of generator.generate(item, rootContext)) {
	                    if (this.objectRegistry.hasObject(yieldedObject)) {
	                        continue;
	                    }
	                    this.registerObject(yieldedObject);
	                }
	            }
	        }
	    }
	    getRegistry() {
	        return this.objectRegistry;
	    }
	}
	TransformOrchestrator.TransformOrchestrator = TransformOrchestrator$1;
	
	return TransformOrchestrator;
}

var TransformProject = {};

var TransformObject = {};

var hasRequiredTransformObject;

function requireTransformObject () {
	if (hasRequiredTransformObject) return TransformObject;
	hasRequiredTransformObject = 1;
	Object.defineProperty(TransformObject, "__esModule", { value: true });
	TransformObject.TransformObject = void 0;
	class TransformObject$1 {
	    constructor(parent, sharedData, deduplicationKey, allowedChildTypes = new Set(), context) {
	        this.allowedChildTypes = allowedChildTypes;
	        this.parent = parent;
	        this.deduplicationKey = deduplicationKey;
	        this._sharedData = sharedData;
	        if (context) {
	            this.context = context;
	        }
	        else {
	            this.context = parent?.context;
	        }
	    }
	    linkTo(primary) {
	        if (primary === this) {
	            throw new Error('Cannot link object to itself');
	        }
	        if (primary.deduplicationKey !== this.deduplicationKey) {
	            throw new Error('Cannot link objects with different deduplication keys');
	        }
	        if (this.deduplicationKey === undefined) {
	            throw new Error('Cannot link objects with no deduplication key');
	        }
	        if (this._sharedData?.compiled !== undefined) {
	            throw new Error('Cannot link object that has already compiled');
	        }
	        this._sharedData = primary._sharedData;
	    }
	    get compiled() {
	        if (this._sharedData === undefined) {
	            this._sharedData = {};
	        }
	        if (this._sharedData.compiled === undefined) {
	            const start = performance.now();
	            this._sharedData.compiled = this.compile();
	            this._sharedData._compilationTimeMs = performance.now() - start;
	        }
	        return this._sharedData.compiled;
	    }
	    get isCompiled() {
	        return this._sharedData?.compiled !== undefined;
	    }
	    canAdd(child) {
	        for (const type of this.allowedChildTypes) {
	            if (child instanceof type) {
	                return true;
	            }
	        }
	        return false;
	    }
	    assertCanAdd(child) {
	        if (!this.canAdd(child)) {
	            throw new Error(`${this.constructor.name} cannot add child of type ${child.constructor.name}`);
	        }
	    }
	    add(_child) {
	        throw new Error(`${this.constructor.name} does not accept children`);
	    }
	    get dependencies() {
	        return [];
	    }
	    get creates() {
	        return [];
	    }
	    findParent(type) {
	        let current = this;
	        while (current !== undefined) {
	            if (current instanceof type) {
	                return current;
	            }
	            current = current.parent;
	        }
	        return undefined;
	    }
	    requireParent(type) {
	        const parent = this.findParent(type);
	        if (!parent) {
	            throw new Error(`${this.constructor.name} requires a parent of type ${type.name}`);
	        }
	        return parent;
	    }
	    static FindParent(object, type) {
	        let current = object.parent;
	        while (current !== undefined) {
	            if (current instanceof type) {
	                return current;
	            }
	            current = current.parent;
	        }
	        throw new Error(`${object.constructor.name} requires a parent of type ${type.name}`);
	    }
	}
	TransformObject.TransformObject = TransformObject$1;
	
	return TransformObject;
}

var TransformSchematic = {};

var TransformComponent = {};

var TransformComponentSymbol = {};

var hasRequiredTransformComponentSymbol;

function requireTransformComponentSymbol () {
	if (hasRequiredTransformComponentSymbol) return TransformComponentSymbol;
	hasRequiredTransformComponentSymbol = 1;
	Object.defineProperty(TransformComponentSymbol, "__esModule", { value: true });
	TransformComponentSymbol.TransformComponentSymbol = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformComponentSymbol$1 extends TransformObject_1.TransformObject {
	    constructor(parent, data) {
	        super(parent, {
	            ref: data.ref,
	            name: data.name,
	            pins: data.pins,
	            drawings: data.drawings,
	            metadata: data.metadata,
	            pinSignals: new Map(),
	        }, `${parent.deduplicationKey}::${data.ref}`, new Set());
	    }
	    setPinSignal(pin, signalId) {
	        this._sharedData.pinSignals.set(pin, signalId);
	    }
	    compile() {
	        this._sharedData.compiled = {
	            result: {
	                ref: this._sharedData.ref,
	                name: this._sharedData.name,
	                pins: this._sharedData.pins,
	                drawings: this._sharedData.drawings,
	                metadata: this._sharedData.metadata,
	            },
	            pinSignals: this._sharedData.pinSignals,
	        };
	        return this._sharedData.compiled;
	    }
	}
	TransformComponentSymbol.TransformComponentSymbol = TransformComponentSymbol$1;
	
	return TransformComponentSymbol;
}

var TransformComponentFootprint = {};

var hasRequiredTransformComponentFootprint;

function requireTransformComponentFootprint () {
	if (hasRequiredTransformComponentFootprint) return TransformComponentFootprint;
	hasRequiredTransformComponentFootprint = 1;
	Object.defineProperty(TransformComponentFootprint, "__esModule", { value: true });
	TransformComponentFootprint.TransformComponentFootprint = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformComponentFootprint$1 extends TransformObject_1.TransformObject {
	    constructor(parent, data) {
	        super(parent, {
	            ref: data.ref,
	            name: data.name,
	            pads: data.pads,
	            drawings: data.drawings,
	            metadata: data.metadata,
	            padSignals: new Map(),
	        }, `${parent.deduplicationKey}::${data.ref}`, new Set());
	    }
	    compile() {
	        this._sharedData.compiled = {
	            result: {
	                ref: this._sharedData.ref,
	                name: this._sharedData.name,
	                pads: this._sharedData.pads,
	                drawings: this._sharedData.drawings,
	                metadata: this._sharedData.metadata,
	            },
	            padSignals: this._sharedData.padSignals,
	        };
	        return this._sharedData.compiled;
	    }
	}
	TransformComponentFootprint.TransformComponentFootprint = TransformComponentFootprint$1;
	
	return TransformComponentFootprint;
}

var hasRequiredTransformComponent;

function requireTransformComponent () {
	if (hasRequiredTransformComponent) return TransformComponent;
	hasRequiredTransformComponent = 1;
	Object.defineProperty(TransformComponent, "__esModule", { value: true });
	TransformComponent.TransformComponent = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformComponentSymbol_1 = requireTransformComponentSymbol();
	const TransformComponentFootprint_1 = requireTransformComponentFootprint();
	class TransformComponent$1 extends TransformObject_1.TransformObject {
	    constructor(parent, args) {
	        super(parent, {
	            ...args,
	            symbolObjects: new Map(),
	            footprintObjects: new Map(),
	        }, args.baseComponent.name, new Set([TransformComponentSymbol_1.TransformComponentSymbol, TransformComponentFootprint_1.TransformComponentFootprint]));
	    }
	    get dependencies() {
	        return [...this._sharedData.symbolObjects.values(), ...this._sharedData.footprintObjects.values()];
	    }
	    add(child) {
	        this.assertCanAdd(child);
	        if (child instanceof TransformComponentSymbol_1.TransformComponentSymbol) {
	            this._sharedData.symbolObjects.set(child.deduplicationKey, child);
	        }
	        else if (child instanceof TransformComponentFootprint_1.TransformComponentFootprint) {
	            this._sharedData.footprintObjects.set(child.deduplicationKey, child);
	        }
	    }
	    linkTo(primary) {
	        for (const symbol of this._sharedData.symbolObjects) {
	            primary._sharedData.symbolObjects.set(symbol[0], symbol[1]);
	        }
	        for (const footprint of this._sharedData.footprintObjects) {
	            primary._sharedData.footprintObjects.set(footprint[0], footprint[1]);
	        }
	        super.linkTo(primary);
	    }
	    compile() {
	        const component = this.getBaseComponent();
	        const seenSymbolRefs = new Set();
	        for (const symbol of this._sharedData.symbolObjects.values()) {
	            const compiled = symbol.compiled;
	            if (seenSymbolRefs.has(compiled.result.ref)) {
	                continue;
	            }
	            seenSymbolRefs.add(compiled.result.ref);
	            component.symbols.push(compiled.result);
	            for (const [pin, signalTemplate] of compiled.pinSignals) {
	                const existingSignal = component.signals.find((s) => s.id === signalTemplate.id);
	                if (existingSignal) {
	                    existingSignal.pinRefs.push(pin.ref);
	                }
	                else {
	                    component.signals.push({
	                        padRefs: [],
	                        name: signalTemplate.id.toString(),
	                        ...signalTemplate,
	                        pinRefs: [pin.ref],
	                    });
	                }
	            }
	        }
	        component.numPins = new Set(component.symbols.flatMap((s) => s.pins.map((p) => p.ref))).size;
	        for (const footprint of this._sharedData.footprintObjects.values()) {
	            component.footprints.push(footprint.compiled.result);
	            for (const [pad, signalTemplate] of footprint.compiled.padSignals) {
	                const existingSignal = component.signals.find((s) => s.id === signalTemplate.id);
	                if (existingSignal) {
	                    existingSignal.padRefs.push(pad.ref);
	                }
	                else {
	                    component.signals.push({
	                        pinRefs: [],
	                        name: signalTemplate.id.toString(),
	                        ...signalTemplate,
	                        padRefs: [pad.ref],
	                    });
	                }
	            }
	        }
	        component.numPads = new Set(component.footprints.flatMap((s) => s.pads.flatMap((p) => p.ref))).size;
	        return component;
	    }
	    getBaseComponent() {
	        return {
	            numPins: 0,
	            numPads: 0,
	            symbols: [],
	            footprints: [],
	            signals: [],
	            ...(this._sharedData.baseComponent ?? {}),
	        };
	    }
	}
	TransformComponent.TransformComponent = TransformComponent$1;
	
	return TransformComponent;
}

var TransformModule = {};

var TransformModulePinConnection = {};

var TransformNetConnection = {};

var TransformSchematicDrawing = {};

var hasRequiredTransformSchematicDrawing;

function requireTransformSchematicDrawing () {
	if (hasRequiredTransformSchematicDrawing) return TransformSchematicDrawing;
	hasRequiredTransformSchematicDrawing = 1;
	Object.defineProperty(TransformSchematicDrawing, "__esModule", { value: true });
	TransformSchematicDrawing.TransformSchematicDrawing = void 0;
	const _ = requireLodash();
	const sigma_engine_shared_1 = requireDist$1();
	const TransformObject_1 = requireTransformObject();
	class TransformSchematicDrawing$1 extends TransformObject_1.TransformObject {
	    constructor(parent, data, netRefObject) {
	        const serialized = JSON.stringify(sigma_engine_shared_1.Utils.sortObjectKeysRecursively(_.omit(data, 'net', 'nets')));
	        super(parent, { data, netRefObject }, `drawing::${serialized}`, new Set());
	        if (netRefObject) {
	            if (data.drawing.type === 'schref') {
	                throw new Error('Cannot add net ref object to schref drawing');
	            }
	            this._sharedData.netRefObject = netRefObject;
	        }
	    }
	    get dependencies() {
	        return this._sharedData.netRefObject ? [this._sharedData.netRefObject] : [];
	    }
	    compile() {
	        const compilationResult = this._sharedData.data;
	        if (this._sharedData.netRefObject) {
	            const drawing = compilationResult.drawing;
	            if (drawing.type === 'schref') {
	                throw new Error('Cannot add net ref object to schref drawing');
	            }
	            drawing.net = this._sharedData.netRefObject.compiled.strCast();
	        }
	        this._sharedData.compiled = compilationResult;
	        return this._sharedData.compiled;
	    }
	}
	TransformSchematicDrawing.TransformSchematicDrawing = TransformSchematicDrawing$1;
	
	return TransformSchematicDrawing;
}

var hasRequiredTransformNetConnection;

function requireTransformNetConnection () {
	if (hasRequiredTransformNetConnection) return TransformNetConnection;
	hasRequiredTransformNetConnection = 1;
	Object.defineProperty(TransformNetConnection, "__esModule", { value: true });
	TransformNetConnection.TransformNetConnection = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	class TransformNetConnection$1 extends TransformObject_1.TransformObject {
	    constructor(parent, data) {
	        if (!parent) {
	            throw new Error('TransformNetConnection must have a parent');
	        }
	        super(parent, {
	            position: data.position,
	            moduleRef: data.moduleRef,
	            moduleName: data.moduleName,
	            pinRef: data.pinRef,
	            pinNumber: data.pinNumber,
	            pinPosition: data.pinPosition,
	            appliesNetName: data.appliesNetName,
	            skipPositionalCombobulation: data.skipPositionalCombobulation,
	            debugDrawings: [],
	        }, undefined, new Set());
	        const debugLayer = this.context?.options?.combobulationDebugLayerId;
	        if (debugLayer !== undefined) {
	            const label = data.appliesNetName ?
	                `${data.moduleName}.${data.pinRef}[=${data.appliesNetName.label}]`
	                : `${data.moduleName}.${data.pinRef}`;
	            this._sharedData.debugDrawings.push(new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [debugLayer],
	                drawing: { type: 'junction', position: data.position, radiusOverride: 0.75 },
	            }), new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [debugLayer],
	                drawing: {
	                    type: 'text',
	                    value: label,
	                    pos: [data.position[0] + 0.5, data.position[1]],
	                    angle: 90,
	                    justify: [-1, -1],
	                    size: 1,
	                    thickness: 1,
	                },
	            }));
	        }
	    }
	    get creates() {
	        return this._sharedData.debugDrawings;
	    }
	    compile() {
	        const result = this.parent.netCombobulator.addConnection(this._sharedData.position, {
	            moduleRef: this._sharedData.moduleRef,
	            moduleName: this._sharedData.moduleName,
	            pinRef: this._sharedData.pinRef,
	            pinNumber: this._sharedData.pinNumber,
	            pinPosition: this._sharedData.pinPosition,
	            appliesNetName: this._sharedData.appliesNetName,
	            skipPositionalCombobulation: this._sharedData.skipPositionalCombobulation,
	        });
	        return result;
	    }
	}
	TransformNetConnection.TransformNetConnection = TransformNetConnection$1;
	
	return TransformNetConnection;
}

var hasRequiredTransformModulePinConnection;

function requireTransformModulePinConnection () {
	if (hasRequiredTransformModulePinConnection) return TransformModulePinConnection;
	hasRequiredTransformModulePinConnection = 1;
	Object.defineProperty(TransformModulePinConnection, "__esModule", { value: true });
	TransformModulePinConnection.TransformModulePinConnection = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformNetConnection_1 = requireTransformNetConnection();
	class TransformModulePinConnection$1 extends TransformObject_1.TransformObject {
	    constructor(parent, props) {
	        super(parent, {
	            ...props,
	            transformNetConnection: null,
	        }, `${props.connection.moduleRef}::${props.connection.pinRef}`, new Set([TransformNetConnection_1.TransformNetConnection]));
	        const connectionParent = this.requireParent(TransformSchematic_1.TransformSchematic);
	        this._sharedData.transformNetConnection = new TransformNetConnection_1.TransformNetConnection(connectionParent, {
	            position: props.position,
	            moduleRef: props.connection.moduleRef,
	            moduleName: props.connection.moduleName,
	            pinRef: props.connection.pinRef,
	            pinNumber: props.connection.pinNumber,
	            pinPosition: props.connection.pinPosition,
	            appliesNetName: props.connection.appliesNetName,
	            skipPositionalCombobulation: props.connection.skipPositionalCombobulation,
	        });
	    }
	    get dependencies() {
	        return [this._sharedData.transformNetConnection];
	    }
	    compile() {
	        return {
	            netRef: this._sharedData.transformNetConnection.compiled,
	            connection: this._sharedData.connection,
	        };
	    }
	}
	TransformModulePinConnection.TransformModulePinConnection = TransformModulePinConnection$1;
	
	return TransformModulePinConnection;
}

var hasRequiredTransformModule;

function requireTransformModule () {
	if (hasRequiredTransformModule) return TransformModule;
	hasRequiredTransformModule = 1;
	Object.defineProperty(TransformModule, "__esModule", { value: true });
	TransformModule.TransformModule = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformModulePinConnection_1 = requireTransformModulePinConnection();
	class TransformModule$1 extends TransformObject_1.TransformObject {
	    constructor(parent, props, component, projectModule) {
	        super(parent, {
	            modProps: props,
	            component,
	            projectModule,
	            pinConnections: new Set(),
	        }, `${parent.deduplicationKey}::mod::${props.ref}`, new Set([TransformModulePinConnection_1.TransformModulePinConnection]));
	        projectModule.registerInstance(this);
	    }
	    add(child) {
	        this.assertCanAdd(child);
	        this._sharedData.pinConnections.add(child);
	    }
	    get dependencies() {
	        return [this._sharedData.component];
	    }
	    get creates() {
	        return [this._sharedData.projectModule];
	    }
	    compile() {
	        const pinNets = Object.fromEntries(Array.from(this._sharedData.pinConnections).map((child) => [child.compiled.connection.pinRef, child.compiled.netRef.strCast()]));
	        return {
	            layer: 'F',
	            ...this._sharedData.modProps,
	            componentRef: this._sharedData.component.compiled.name,
	            pinNets,
	        };
	    }
	}
	TransformModule.TransformModule = TransformModule$1;
	
	return TransformModule;
}

var TransformNetWire = {};

var hasRequiredTransformNetWire;

function requireTransformNetWire () {
	if (hasRequiredTransformNetWire) return TransformNetWire;
	hasRequiredTransformNetWire = 1;
	Object.defineProperty(TransformNetWire, "__esModule", { value: true });
	TransformNetWire.TransformNetWire = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	class TransformNetWire$1 extends TransformObject_1.TransformObject {
	    constructor(parent, from, to, draw, fromDimension, toDimension, strategyMetadata) {
	        super(parent, {
	            from,
	            to,
	            fromDimension,
	            toDimension,
	            strategyMetadata,
	            debugDrawings: [],
	        }, undefined, new Set());
	        if (draw) {
	            this._sharedData.draw = new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, draw, this);
	        }
	        const debugLayer = this.context?.options?.combobulationDebugLayerId;
	        if (debugLayer !== undefined) {
	            this._sharedData.debugDrawings.push(new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [debugLayer],
	                drawing: { type: 'junction', position: from, radiusOverride: 0.5 },
	            }), new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [debugLayer],
	                drawing: { type: 'junction', position: to, radiusOverride: 0.5 },
	            }));
	        }
	    }
	    get creates() {
	        return [...(this._sharedData.draw ? [this._sharedData.draw] : []), ...this._sharedData.debugDrawings];
	    }
	    compile() {
	        const { from, to, fromDimension, toDimension, strategyMetadata } = this._sharedData;
	        return this.parent.netCombobulator.addWire({
	            from: { position: from, dimension: fromDimension ?? this.parent.netCombobulator.defaultDimension.name },
	            to: { position: to, dimension: toDimension ?? this.parent.netCombobulator.defaultDimension.name },
	            strategyMetadata,
	        });
	    }
	}
	TransformNetWire.TransformNetWire = TransformNetWire$1;
	
	return TransformNetWire;
}

var TransformNetLabel = {};

var hasRequiredTransformNetLabel;

function requireTransformNetLabel () {
	if (hasRequiredTransformNetLabel) return TransformNetLabel;
	hasRequiredTransformNetLabel = 1;
	Object.defineProperty(TransformNetLabel, "__esModule", { value: true });
	TransformNetLabel.TransformNetLabel = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	class TransformNetLabel$1 extends TransformObject_1.TransformObject {
	    constructor(parent, data, draw) {
	        super(parent, {
	            position: data.position,
	            originalLabel: data.originalLabel,
	            labelType: data.labelType,
	            strategyMetadata: data.strategyMetadata,
	            dimensions: data.dimensions,
	            debugDrawings: [],
	        }, undefined, new Set());
	        if (draw) {
	            this._sharedData.draw = new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, draw, this);
	        }
	        const debugLayer = this.context?.options?.combobulationDebugLayerId;
	        if (debugLayer !== undefined) {
	            this._sharedData.debugDrawings.push(new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [debugLayer],
	                drawing: { type: 'junction', position: data.position, radiusOverride: 0.75 },
	            }), new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [debugLayer],
	                drawing: {
	                    type: 'text',
	                    value: `L:${data.originalLabel}`,
	                    pos: [data.position[0] + 0.5, data.position[1]],
	                    angle: 0,
	                    justify: [-1, -1],
	                    size: 1,
	                    thickness: 1,
	                },
	            }));
	        }
	    }
	    get creates() {
	        return [...(this._sharedData.draw ? [this._sharedData.draw] : []), ...this._sharedData.debugDrawings];
	    }
	    compile() {
	        const { position, originalLabel, labelType, strategyMetadata, dimensions } = this._sharedData;
	        if (dimensions?.length) {
	            const endpoints = dimensions.map((d) => ({ position, dimension: d }));
	            return this.parent.netCombobulator.setLabel(endpoints, originalLabel, labelType, strategyMetadata);
	        }
	        return this.parent.netCombobulator.setLabel(position, originalLabel, labelType, strategyMetadata);
	    }
	}
	TransformNetLabel.TransformNetLabel = TransformNetLabel$1;
	
	return TransformNetLabel;
}

var TransformSchematicModelInstanceConfiguration = {};

var hasRequiredTransformSchematicModelInstanceConfiguration;

function requireTransformSchematicModelInstanceConfiguration () {
	if (hasRequiredTransformSchematicModelInstanceConfiguration) return TransformSchematicModelInstanceConfiguration;
	hasRequiredTransformSchematicModelInstanceConfiguration = 1;
	Object.defineProperty(TransformSchematicModelInstanceConfiguration, "__esModule", { value: true });
	TransformSchematicModelInstanceConfiguration.TransformSchematicModelInstanceConfiguration = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformSchematicModelInstanceConfiguration$1 extends TransformObject_1.TransformObject {
	    constructor(parent, baseConfiguration) {
	        super(parent, {
	            baseConfiguration,
	        }, baseConfiguration.instanceId, new Set());
	        this.deduplicationKey = baseConfiguration.instanceId;
	    }
	    compile() {
	        return this._sharedData.baseConfiguration;
	    }
	}
	TransformSchematicModelInstanceConfiguration.TransformSchematicModelInstanceConfiguration = TransformSchematicModelInstanceConfiguration$1;
	
	return TransformSchematicModelInstanceConfiguration;
}

var TransformMultiInstanceDisplayModule = {};

var hasRequiredTransformMultiInstanceDisplayModule;

function requireTransformMultiInstanceDisplayModule () {
	if (hasRequiredTransformMultiInstanceDisplayModule) return TransformMultiInstanceDisplayModule;
	hasRequiredTransformMultiInstanceDisplayModule = 1;
	Object.defineProperty(TransformMultiInstanceDisplayModule, "__esModule", { value: true });
	TransformMultiInstanceDisplayModule.TransformMultiInstanceDisplayModule = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformModulePinConnection_1 = requireTransformModulePinConnection();
	class TransformMultiInstanceDisplayModule$1 extends TransformObject_1.TransformObject {
	    constructor(parent, props, component) {
	        super(parent, {
	            modProps: props,
	            component,
	            pinConnections: new Set(),
	        }, `${parent.deduplicationKey}:midm::${props.ref}`, new Set([TransformModulePinConnection_1.TransformModulePinConnection]));
	    }
	    add(child) {
	        this.assertCanAdd(child);
	        this._sharedData.pinConnections.add(child);
	    }
	    get dependencies() {
	        return [this._sharedData.component];
	    }
	    compile() {
	        const pinNets = Object.fromEntries(Array.from(this._sharedData.pinConnections).map((child) => [child.compiled.connection.pinRef, child.compiled.netRef.strCast()]));
	        return {
	            layer: 'F',
	            ...this._sharedData.modProps,
	            componentRef: this._sharedData.component.compiled.name,
	            pinNets,
	        };
	    }
	}
	TransformMultiInstanceDisplayModule.TransformMultiInstanceDisplayModule = TransformMultiInstanceDisplayModule$1;
	
	return TransformMultiInstanceDisplayModule;
}

var TransformSchematicLayerSet = {};

var hasRequiredTransformSchematicLayerSet;

function requireTransformSchematicLayerSet () {
	if (hasRequiredTransformSchematicLayerSet) return TransformSchematicLayerSet;
	hasRequiredTransformSchematicLayerSet = 1;
	Object.defineProperty(TransformSchematicLayerSet, "__esModule", { value: true });
	TransformSchematicLayerSet.TransformSchematicLayerSet = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformSchematicLayerSet$1 extends TransformObject_1.TransformObject {
	    constructor(parent, baseLayerSet) {
	        super(parent, {
	            baseLayerSet,
	        }, baseLayerSet.id, new Set());
	        this.deduplicationKey = baseLayerSet.id;
	    }
	    get layerSetId() {
	        return this._sharedData.baseLayerSet.id;
	    }
	    compile() {
	        return this._sharedData.baseLayerSet;
	    }
	}
	TransformSchematicLayerSet.TransformSchematicLayerSet = TransformSchematicLayerSet$1;
	
	return TransformSchematicLayerSet;
}

var TransformProcessingResult = {};

var hasRequiredTransformProcessingResult;

function requireTransformProcessingResult () {
	if (hasRequiredTransformProcessingResult) return TransformProcessingResult;
	hasRequiredTransformProcessingResult = 1;
	Object.defineProperty(TransformProcessingResult, "__esModule", { value: true });
	TransformProcessingResult.TransformProcessingResult = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformProcessingResult$1 extends TransformObject_1.TransformObject {
	    constructor(parent, baseResult) {
	        super(parent, {
	            baseResult,
	            innerResults: new Set(),
	        }, undefined, new Set([TransformProcessingResult$1]));
	    }
	    add(child) {
	        this.assertCanAdd(child);
	        this._sharedData.innerResults.add(child);
	    }
	    compile() {
	        const baseResult = this._sharedData.baseResult;
	        const innerResults = (baseResult.innerResults ?? []).concat(Array.from(this._sharedData.innerResults).map((child) => child.compile()));
	        return {
	            ...baseResult,
	            innerResults: innerResults.length > 0 ? innerResults : undefined,
	        };
	    }
	}
	TransformProcessingResult.TransformProcessingResult = TransformProcessingResult$1;
	
	return TransformProcessingResult;
}

var TransformProjectLayerSet = {};

var hasRequiredTransformProjectLayerSet;

function requireTransformProjectLayerSet () {
	if (hasRequiredTransformProjectLayerSet) return TransformProjectLayerSet;
	hasRequiredTransformProjectLayerSet = 1;
	Object.defineProperty(TransformProjectLayerSet, "__esModule", { value: true });
	TransformProjectLayerSet.TransformProjectLayerSet = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformProjectLayerSet$1 extends TransformObject_1.TransformObject {
	    constructor(parent, baseLayerSet) {
	        super(parent, {
	            baseLayerSet,
	        }, baseLayerSet.id, new Set());
	    }
	    static From(parent, other) {
	        return new TransformProjectLayerSet$1(parent, other.compiled);
	    }
	    compile() {
	        return this._sharedData.baseLayerSet;
	    }
	}
	TransformProjectLayerSet.TransformProjectLayerSet = TransformProjectLayerSet$1;
	
	return TransformProjectLayerSet;
}

var mapDrawingNetLabels = {};

var hasRequiredMapDrawingNetLabels;

function requireMapDrawingNetLabels () {
	if (hasRequiredMapDrawingNetLabels) return mapDrawingNetLabels;
	hasRequiredMapDrawingNetLabels = 1;
	Object.defineProperty(mapDrawingNetLabels, "__esModule", { value: true });
	mapDrawingNetLabels.mapDrawingNetLabels = mapDrawingNetLabels$1;
	function mapDrawingNetLabels$1(drawing, mapNet) {
	    let result = drawing;
	    if (result.net !== undefined) {
	        const mapped = mapNet(String(result.net));
	        if (mapped === null) {
	            result = { ...result };
	            delete result.net;
	        }
	        else if (mapped !== undefined) {
	            result = { ...result, net: mapped };
	        }
	    }
	    if (result.type === 'group') {
	        const group = result;
	        let childrenChanged = false;
	        const children = group.drawings.map((child) => {
	            const mappedChild = mapDrawingNetLabels$1(child, mapNet);
	            childrenChanged ||= mappedChild !== child;
	            return mappedChild;
	        });
	        if (childrenChanged) {
	            result = { ...result, drawings: children };
	        }
	    }
	    return result;
	}
	
	return mapDrawingNetLabels;
}

var hasRequiredTransformSchematic;

function requireTransformSchematic () {
	if (hasRequiredTransformSchematic) return TransformSchematic;
	hasRequiredTransformSchematic = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TransformSchematic = exports.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const TransformObject_1 = requireTransformObject();
		const TransformProject_1 = requireTransformProject();
		const TransformComponent_1 = requireTransformComponent();
		const TransformModule_1 = requireTransformModule();
		const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
		const TransformNetWire_1 = requireTransformNetWire();
		const TransformNetLabel_1 = requireTransformNetLabel();
		const TransformNetConnection_1 = requireTransformNetConnection();
		const TransformSchematicModelInstanceConfiguration_1 = requireTransformSchematicModelInstanceConfiguration();
		const TransformMultiInstanceDisplayModule_1 = requireTransformMultiInstanceDisplayModule();
		const TransformSchematicLayerSet_1 = requireTransformSchematicLayerSet();
		const Area_1 = requireArea();
		const TransformProcessingResult_1 = requireTransformProcessingResult();
		const TransformProjectLayerSet_1 = requireTransformProjectLayerSet();
		const mapDrawingNetLabels_1 = requireMapDrawingNetLabels();
		exports.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL = Symbol('TRANSFORM_SCHEMATIC_DRAWING_ID');
		class TransformSchematic extends TransformObject_1.TransformObject {
		    constructor(parent, args, netCombobulator, context) {
		        super(parent, {
		            ...args,
		            modules: new Set(),
		            drawings: new Set(),
		            wires: new Set(),
		            labels: new Set(),
		            netConnections: new Set(),
		            instanceConfigurations: new Set(),
		            multiInstanceDisplayModules: new Set(),
		            layerSets: new Set(),
		        }, args.designId, new Set([
		            TransformModule_1.TransformModule,
		            TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule,
		            TransformNetConnection_1.TransformNetConnection,
		            TransformNetLabel_1.TransformNetLabel,
		            TransformNetWire_1.TransformNetWire,
		            TransformSchematicDrawing_1.TransformSchematicDrawing,
		            TransformSchematicLayerSet_1.TransformSchematicLayerSet,
		            TransformSchematicModelInstanceConfiguration_1.TransformSchematicModelInstanceConfiguration,
		        ]), context);
		        this.netCombobulator = netCombobulator;
		        this._dependencies = [];
		    }
		    get designId() {
		        return this._sharedData.designId;
		    }
		    get instance() {
		        return this._sharedData.instance;
		    }
		    get hierarchyEntry() {
		        return this._sharedData.instance.hierarchyEntry;
		    }
		    get sourceFile() {
		        return this._sharedData.sourceFile;
		    }
		    add(child) {
		        this.assertCanAdd(child);
		        this._dependencies.push(child);
		        if (child instanceof TransformModule_1.TransformModule) {
		            this._sharedData.modules.add(child);
		        }
		        else if (child instanceof TransformSchematicDrawing_1.TransformSchematicDrawing) {
		            this._sharedData.drawings.add(child);
		        }
		        else if (child instanceof TransformNetWire_1.TransformNetWire) {
		            this._sharedData.wires.add(child);
		        }
		        else if (child instanceof TransformNetLabel_1.TransformNetLabel) {
		            this._sharedData.labels.add(child);
		        }
		        else if (child instanceof TransformNetConnection_1.TransformNetConnection) {
		            this._sharedData.netConnections.add(child);
		        }
		        else if (child instanceof TransformSchematicModelInstanceConfiguration_1.TransformSchematicModelInstanceConfiguration) {
		            this._sharedData.instanceConfigurations.add(child);
		        }
		        else if (child instanceof TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule) {
		            this._sharedData.multiInstanceDisplayModules.add(child);
		        }
		        else if (child instanceof TransformSchematicLayerSet_1.TransformSchematicLayerSet) {
		            this._sharedData.layerSets.add(child);
		            const layerSetIds = this.getLayerSetIds();
		            if (layerSetIds.length > 1) {
		                this._dependencies.push(new TransformProcessingResult_1.TransformProcessingResult(this, {
		                    type: 'schematic-multiple-layer-sets',
		                    message: `Multiple layer set ids found in schematic ${this._sharedData.designId}, will use first: ${layerSetIds.values().next().value}`,
		                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                    foundLayerSetIds: Array.from(layerSetIds),
		                }));
		            }
		            else {
		                this._dependencies.push(TransformProjectLayerSet_1.TransformProjectLayerSet.From(this.requireParent(TransformProject_1.TransformProject), child));
		            }
		        }
		    }
		    compile() {
		        const compiledModules = Array.from(this._sharedData.modules).map((child) => child.compiled);
		        for (const wire of this._sharedData.wires) {
		            void wire.compiled;
		        }
		        for (const label of this._sharedData.labels) {
		            void label.compiled;
		        }
		        for (const conn of this._sharedData.netConnections) {
		            void conn.compiled;
		        }
		        Array.from(this._sharedData.instanceConfigurations).map((child) => child.compiled);
		        Array.from(this._sharedData.multiInstanceDisplayModules).map((child) => child.compiled);
		        const layerSetIds = this.getLayerSetIds();
		        const drawings = {};
		        for (const transformDrawing of this._sharedData.drawings) {
		            const { layers, drawing } = transformDrawing.compiled;
		            for (const layer of layers) {
		                const drawingWithId = {
		                    ...drawing,
		                    [exports.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL]: transformDrawing.deduplicationKey,
		                };
		                drawings[layer] ??= [];
		                drawings[layer].push(drawingWithId);
		            }
		        }
		        const netCombobulatorStart = performance.now();
		        const netCbNets = this.netCombobulator.computeNets();
		        const minConnections = this.context?.options?.minNetConnections;
		        const netsMustHaveWireOrMultipleConnections = this.context?.options?.netsMustHaveWireOrMultipleConnections;
		        const nets = Object.fromEntries(Array.from(netCbNets.values())
		            .filter((net) => {
		            if (minConnections !== undefined && net.connections.length < minConnections) {
		                return false;
		            }
		            if (netsMustHaveWireOrMultipleConnections && net.wires.length === 0 && net.connections.length <= 1) {
		                const hasAppliedNameOnConnection = net.connections.some(({ connection }) => connection.appliesNetName !== undefined);
		                const hasNamingLabel = net.labels.some((label) => label.canNameNet);
		                if (!hasAppliedNameOnConnection && !hasNamingLabel) {
		                    return false;
		                }
		            }
		            return true;
		        })
		            .map((net) => [
		            net.name.valueOf(),
		            {
		                name: net.name.valueOf(),
		                connections: net.connections.map(({ connection }) => ({
		                    moduleRef: connection.moduleRef,
		                    moduleName: connection.moduleName,
		                    pinRef: connection.pinRef,
		                })),
		            },
		        ]));
		        const moduleByRef = new Map(compiledModules.map((m) => [m.ref, m]));
		        const droppedNetNames = new Set();
		        for (const net of netCbNets.values()) {
		            if (!(net.name.valueOf() in nets)) {
		                droppedNetNames.add(net.name.valueOf());
		                continue;
		            }
		            for (const { connection } of net.connections) {
		                const mod = moduleByRef.get(connection.moduleRef);
		                if (mod) {
		                    mod.pinNets[connection.pinRef] = net.name.valueOf();
		                }
		            }
		        }
		        if (droppedNetNames.size > 0) {
		            for (const [layer, layerDrawings] of Object.entries(drawings)) {
		                drawings[layer] = layerDrawings.map((drawing) => (0, mapDrawingNetLabels_1.mapDrawingNetLabels)(drawing, (net) => (droppedNetNames.has(net) ? null : undefined)));
		            }
		        }
		        this._sharedData._combobulatorTimeMs = performance.now() - netCombobulatorStart;
		        const model = {
		            modelType: 'schematic',
		            designId: this._sharedData.designId,
		            designName: this._sharedData.designName,
		            sourceFile: this._sharedData.sourceFile,
		            sheetIndex: 0,
		            layerSet: layerSetIds[0],
		            grid: this._sharedData.grid,
		            font: this._sharedData.font,
		            drawings,
		            edges_bbox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
		            initial_zoombox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
		            nets,
		            modules: compiledModules,
		            ports: {},
		            metadata: this._sharedData.metadata,
		            warnings: [],
		        };
		        this.computeBoundingBoxes(model);
		        return model;
		    }
		    computeBoundingBoxes(model) {
		        const components = {};
		        for (const mod of this._sharedData.modules) {
		            const dep = mod.dependencies[0];
		            if (dep instanceof TransformComponent_1.TransformComponent) {
		                const compiled = dep.compiled;
		                if (!components[compiled.name]) {
		                    components[compiled.name] = compiled;
		                }
		            }
		        }
		        const core = { components, warnings: [] };
		        const projectLayers = Array.from(this._sharedData.layerSets).flatMap((child) => child.compiled.layers ?? []);
		        const bbox = new Area_1.default();
		        model.edges_bbox = bbox.extendFromSchModel(core, model, projectLayers, model.warnings, true).toModelBoundingBox();
		        const visibleBbox = new Area_1.default();
		        model.initial_zoombox = visibleBbox
		            .extendFromSchModel(core, model, projectLayers, model.warnings, false)
		            .toModelBoundingBox(model.edges_bbox);
		        const MAX_ASPECT_RATIO = 2;
		        model.edges_bbox = (0, sigma_engine_shared_1.padBboxToAspectRatio)(model.edges_bbox, MAX_ASPECT_RATIO);
		        model.initial_zoombox = (0, sigma_engine_shared_1.padBboxToAspectRatio)(model.initial_zoombox, MAX_ASPECT_RATIO);
		    }
		    getLayerSetIds() {
		        const layerSets = Array.from(this._sharedData.layerSets).map((child) => child.compiled);
		        return Array.from(new Set(layerSets.map((layerSet) => layerSet.id)));
		    }
		}
		exports.TransformSchematic = TransformSchematic;
		
	} (TransformSchematic));
	return TransformSchematic;
}

var TransformMultiInstanceSchematic = {};

var hasRequiredTransformMultiInstanceSchematic;

function requireTransformMultiInstanceSchematic () {
	if (hasRequiredTransformMultiInstanceSchematic) return TransformMultiInstanceSchematic;
	hasRequiredTransformMultiInstanceSchematic = 1;
	Object.defineProperty(TransformMultiInstanceSchematic, "__esModule", { value: true });
	TransformMultiInstanceSchematic.TransformMultiInstanceSchematic = void 0;
	const _ = requireLodash();
	const TransformObject_1 = requireTransformObject();
	const TransformSchematicModelInstanceConfiguration_1 = requireTransformSchematicModelInstanceConfiguration();
	const TransformMultiInstanceDisplayModule_1 = requireTransformMultiInstanceDisplayModule();
	const TransformSchematic_1 = requireTransformSchematic();
	class TransformMultiInstanceSchematic$1 extends TransformObject_1.TransformObject {
	    constructor(parent, args, netCombobulator, context) {
	        super(parent, {
	            ...args,
	            schematics: new Set(),
	            multiInstanceDisplayModules: new Set(),
	        }, args.designId, new Set([
	            TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule,
	            TransformSchematic_1.TransformSchematic,
	            TransformSchematicModelInstanceConfiguration_1.TransformSchematicModelInstanceConfiguration,
	        ]), context);
	        this.netCombobulator = netCombobulator;
	        this._dependencies = [];
	    }
	    get designId() {
	        return this._sharedData.designId;
	    }
	    get childSchematics() {
	        return this._sharedData.schematics;
	    }
	    add(child) {
	        this.assertCanAdd(child);
	        this._dependencies.push(child);
	        if (child instanceof TransformSchematic_1.TransformSchematic) {
	            this._sharedData.schematics.add(child);
	        }
	        else if (child instanceof TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule) {
	            this._sharedData.multiInstanceDisplayModules.add(child);
	        }
	    }
	    compile() {
	        const schematics = this.getAndVerifyChildSchematics();
	        const modules = this.mergeModules(schematics);
	        const nets = this.mergeNets(schematics);
	        const drawings = this.mergeDrawings(schematics);
	        const firstSchematic = schematics[0];
	        const compiledMultiInstanceDisplayModules = Array.from(this._sharedData.multiInstanceDisplayModules).map((child) => child.compiled);
	        const commonMetadata = this.findCommonMetadata(schematics);
	        const multiInstanceConfiguration = {
	            multiInstanceModules: compiledMultiInstanceDisplayModules,
	            instances: schematics.map((s) => ({
	                instanceSheetIndex: s.sheetIndex,
	                instanceName: s.designName ?? s.designId,
	                instanceId: s.designId,
	                sheetDisplayPath: s.sheetDisplayPath,
	                metadata: this.findUncommonMetadata(commonMetadata, s),
	            })),
	        };
	        const model = {
	            modelType: 'schematic',
	            designId: this._sharedData.designId,
	            designName: this._sharedData.designName,
	            sourceFile: this._sharedData.sourceFile,
	            sheetIndex: this._sharedData.sheetIndex,
	            sheetDisplayPath: this._sharedData.sheetDisplayPath,
	            layerSet: firstSchematic.layerSet,
	            font: firstSchematic.font,
	            grid: this._sharedData.grid,
	            drawings,
	            edges_bbox: this.mergeBboxes(schematics, 'edges_bbox'),
	            initial_zoombox: this.mergeBboxes(schematics, 'initial_zoombox'),
	            nets,
	            modules,
	            ports: {},
	            metadata: this._sharedData.metadata,
	            warnings: Array.from(new Set(schematics.flatMap((s) => s.warnings))),
	            multiInstanceConfiguration,
	        };
	        return model;
	    }
	    findCommonMetadata(schematics) {
	        const metadata = {};
	        const [first, ...rest] = schematics;
	        const includeKeys = new Set(Object.keys(first.metadata ?? {}));
	        for (const other of rest) {
	            for (const key of Object.keys(other.metadata ?? {})) {
	                if (other.metadata?.[key] !== first.metadata?.[key]) {
	                    includeKeys.delete(key);
	                }
	            }
	            for (const key of includeKeys) {
	                if (other.metadata?.[key] !== first.metadata?.[key]) {
	                    includeKeys.delete(key);
	                }
	            }
	        }
	        for (const key of includeKeys) {
	            metadata[key] = first.metadata?.[key];
	        }
	        return metadata;
	    }
	    findUncommonMetadata(commonMetadata, schematic) {
	        const metadata = {};
	        for (const [key, value] of Object.entries(schematic.metadata ?? {})) {
	            if (commonMetadata[key] !== value) {
	                metadata[key] = value;
	            }
	        }
	        return metadata;
	    }
	    mergeDrawings(schematics) {
	        const mergedDrawings = {};
	        for (const schematic of schematics) {
	            for (const [layerId, layerDrawings] of Object.entries(schematic.drawings)) {
	                for (const drawing of layerDrawings) {
	                    const drawingId = drawing[TransformSchematic_1.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL];
	                    if (!drawingId) {
	                        throw new Error(`Drawing without ID found in multi-instance schematic`);
	                    }
	                    if (!mergedDrawings[layerId]) {
	                        mergedDrawings[layerId] = [];
	                    }
	                    const mergedDrawing = mergedDrawings[layerId].find((d) => {
	                        const dId = d[TransformSchematic_1.TRANSFORM_SCHEMATIC_DRAWING_ID_SYMBOL];
	                        return dId === drawingId;
	                    });
	                    if (!mergedDrawing) {
	                        if (drawing.type === 'schref') {
	                            mergedDrawings[layerId].push(drawing);
	                            continue;
	                        }
	                        let nets = drawing.nets;
	                        if (drawing.net) {
	                            if (nets) {
	                                throw new Error(`Drawing ${drawingId} has both net and nets properties set`);
	                            }
	                            const newNet = {
	                                i: schematic.designId,
	                                n: drawing.net,
	                            };
	                            nets = [newNet];
	                        }
	                        mergedDrawings[layerId].push({
	                            ...drawing,
	                            net: undefined,
	                            nets,
	                        });
	                        continue;
	                    }
	                    if (drawing.type === 'schref') {
	                        continue;
	                    }
	                    if (drawing.net) {
	                        if (!mergedDrawing.nets) {
	                            mergedDrawing.nets = [];
	                        }
	                        mergedDrawing.nets.push({
	                            i: schematic.designId,
	                            n: drawing.net,
	                        });
	                    }
	                }
	            }
	        }
	        return mergedDrawings;
	    }
	    mergeNets(schematics) {
	        const nets = {};
	        for (const schematic of schematics) {
	            for (const [netName, net] of Object.entries(schematic.nets)) {
	                if (netName in nets) {
	                    nets[netName].connections.push(...net.connections);
	                    if (net.diffPair) {
	                        if (nets[netName].diffPair) {
	                            if (!_.isEqual(nets[netName].diffPair.pairName, net.diffPair?.pairName)) {
	                                throw new Error(`Conflicting differential pairs found for net ${netName}`);
	                            }
	                        }
	                        else {
	                            nets[netName].diffPair = net.diffPair;
	                        }
	                    }
	                    if (net.metadata) {
	                        nets[netName].metadata = {
	                            ...(nets[netName].metadata ?? {}),
	                            ...net.metadata,
	                        };
	                    }
	                    if (net.localName) {
	                        if (nets[netName].localName) {
	                            if (nets[netName].localName !== net.localName) {
	                                throw new Error(`Conflicting local names found for net ${netName}`);
	                            }
	                        }
	                        else {
	                            nets[netName].localName = net.localName;
	                        }
	                    }
	                    if (net.localNames) {
	                        if (nets[netName].localNames) {
	                            nets[netName].localNames = Array.from(new Set([...nets[netName].localNames, ...net.localNames]));
	                        }
	                        else {
	                            nets[netName].localNames = net.localNames;
	                        }
	                    }
	                    if (net.netLabels) {
	                        if (nets[netName].netLabels) {
	                            nets[netName].netLabels.push(...net.netLabels);
	                        }
	                        else {
	                            nets[netName].netLabels = net.netLabels;
	                        }
	                    }
	                }
	                else {
	                    nets[netName] = net;
	                }
	            }
	        }
	        return nets;
	    }
	    mergeModules(schematics) {
	        const moduleRefs = new Set();
	        const modules = [];
	        for (const schematic of schematics) {
	            for (const module of schematic.modules) {
	                if (moduleRefs.has(module.ref)) {
	                    throw new Error(`Duplicate module ref ${module.ref} found in multi-instance schematic`);
	                }
	                moduleRefs.add(module.ref);
	                modules.push(module);
	            }
	        }
	        return modules;
	    }
	    mergeBboxes(schematics, field) {
	        return {
	            minx: Math.min(...schematics.map((s) => s[field].minx)),
	            miny: Math.min(...schematics.map((s) => s[field].miny)),
	            maxx: Math.max(...schematics.map((s) => s[field].maxx)),
	            maxy: Math.max(...schematics.map((s) => s[field].maxy)),
	        };
	    }
	    getAndVerifyChildSchematics() {
	        const schematics = Array.from(this._sharedData.schematics).map((child) => child.compiled);
	        if (schematics.length === 0) {
	            throw new Error(`No child schematics found`);
	        }
	        const designIds = new Set(schematics.map((s) => s.designId));
	        if (designIds.size !== schematics.length) {
	            throw new Error(`Duplicate design ids found in child schematics`);
	        }
	        const [first, ...rest] = schematics;
	        if (first.ports && Object.keys(first.ports).length > 0) {
	            throw new Error(`Ports not yet supported on child schematics of a multi-instance schematic`);
	        }
	        for (const other of rest) {
	            if (other.sourceFile !== first.sourceFile) {
	                throw new Error(`All child schematics must have the same source file`);
	            }
	            else if (other.layerSet !== first.layerSet) {
	                throw new Error(`All child schematics must have the same layer set`);
	            }
	            else if (!_.isEqual(other.ports, first.ports)) {
	                throw new Error(`All child schematics must have the same ports`);
	            }
	        }
	        return schematics;
	    }
	}
	TransformMultiInstanceSchematic.TransformMultiInstanceSchematic = TransformMultiInstanceSchematic$1;
	
	return TransformMultiInstanceSchematic;
}

var TransformProjectFont = {};

var hasRequiredTransformProjectFont;

function requireTransformProjectFont () {
	if (hasRequiredTransformProjectFont) return TransformProjectFont;
	hasRequiredTransformProjectFont = 1;
	Object.defineProperty(TransformProjectFont, "__esModule", { value: true });
	TransformProjectFont.TransformProjectFont = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformProjectFont$1 extends TransformObject_1.TransformObject {
	    constructor(parent, baseFont) {
	        super(parent, {
	            baseFont: baseFont.type === 'browser' ? baseFont : { ...baseFont, glyphs: {} },
	        }, baseFont.name, new Set());
	        this.deduplicationKey = baseFont.name;
	    }
	    compile() {
	        return this._sharedData.baseFont;
	    }
	}
	TransformProjectFont.TransformProjectFont = TransformProjectFont$1;
	
	return TransformProjectFont;
}

var TransformProjectModule = {};

var hasRequiredTransformProjectModule;

function requireTransformProjectModule () {
	if (hasRequiredTransformProjectModule) return TransformProjectModule;
	hasRequiredTransformProjectModule = 1;
	Object.defineProperty(TransformProjectModule, "__esModule", { value: true });
	TransformProjectModule.TransformProjectModule = void 0;
	const TransformObject_1 = requireTransformObject();
	const TransformProject_1 = requireTransformProject();
	const TransformMultiInstanceSchematic_1 = requireTransformMultiInstanceSchematic();
	class TransformProjectModule$1 extends TransformObject_1.TransformObject {
	    constructor(parent, props) {
	        super(parent, {
	            name: props.name,
	            component: props.component,
	            modules: new Set(),
	            model: props.model,
	            metadata: props.metadata,
	            value: props.value,
	            isDnp: props.isDnp,
	            hideInBom: props.hideInBom,
	            catalogRef: props.catalogRef,
	        }, props.name, new Set());
	        this.deduplicationKey = props.name;
	    }
	    registerInstance(module) {
	        this._sharedData.modules.add(module);
	    }
	    linkTo(primary) {
	        for (const mod of this._sharedData.modules) {
	            primary._sharedData.modules.add(mod);
	        }
	        super.linkTo(primary);
	    }
	    get dependencies() {
	        return [this._sharedData.component, ...this._sharedData.modules];
	    }
	    compile() {
	        const componentRef = this._sharedData.component.compiled.name;
	        let metadata;
	        for (const mod of this._sharedData.modules) {
	            if (mod.compiled.metadata) {
	                metadata ??= {};
	                Object.assign(metadata, mod.compiled.metadata);
	            }
	        }
	        if (this._sharedData.metadata) {
	            metadata ??= {};
	            Object.assign(metadata, this._sharedData.metadata);
	        }
	        const instances = Array.from(this._sharedData.modules).map((mod) => ({
	            ref: mod.compiled.ref,
	            designId: this.resolveDesignId(mod),
	        }));
	        return {
	            name: this._sharedData.name,
	            componentRef,
	            instances,
	            value: this._sharedData.value ?? metadata?.['Value']?.toString(),
	            model: this._sharedData.model,
	            metadata,
	            isDnp: this._sharedData.isDnp,
	            hideInBom: this._sharedData.hideInBom,
	            catalogRef: this._sharedData.catalogRef,
	        };
	    }
	    resolveDesignId(mod) {
	        let current = mod.parent;
	        while (current !== undefined) {
	            if (current instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	                return current.designId;
	            }
	            if (current instanceof TransformProject_1.TransformProject) {
	                break;
	            }
	            current = current.parent;
	        }
	        return mod.parent.designId;
	    }
	}
	TransformProjectModule.TransformProjectModule = TransformProjectModule$1;
	
	return TransformProjectModule;
}

var TransformImageExtraction = {};

var hasRequiredTransformImageExtraction;

function requireTransformImageExtraction () {
	if (hasRequiredTransformImageExtraction) return TransformImageExtraction;
	hasRequiredTransformImageExtraction = 1;
	Object.defineProperty(TransformImageExtraction, "__esModule", { value: true });
	TransformImageExtraction.TransformImageExtraction = void 0;
	const TransformObject_1 = requireTransformObject();
	class TransformImageExtraction$1 extends TransformObject_1.TransformObject {
	    constructor(parent, ref, name, data, mimeType) {
	        super(parent, { ref, name, data, mimeType }, ref, new Set());
	    }
	    compile() {
	        return {
	            ref: this._sharedData.ref,
	            name: this._sharedData.name,
	            data: this._sharedData.data,
	            mimeType: this._sharedData.mimeType,
	        };
	    }
	}
	TransformImageExtraction.TransformImageExtraction = TransformImageExtraction$1;
	
	return TransformImageExtraction;
}

var hasRequiredTransformProject;

function requireTransformProject () {
	if (hasRequiredTransformProject) return TransformProject;
	hasRequiredTransformProject = 1;
	Object.defineProperty(TransformProject, "__esModule", { value: true });
	TransformProject.TransformProject = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformObject_1 = requireTransformObject();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformMultiInstanceSchematic_1 = requireTransformMultiInstanceSchematic();
	const TransformComponent_1 = requireTransformComponent();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const TransformProjectFont_1 = requireTransformProjectFont();
	const TransformProjectLayerSet_1 = requireTransformProjectLayerSet();
	const TransformProjectModule_1 = requireTransformProjectModule();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const Font_1 = requireFont();
	const mapDrawingNetLabels_1 = requireMapDrawingNetLabels();
	class TransformProject$1 extends TransformObject_1.TransformObject {
	    constructor(args, context) {
	        super(undefined, {
	            ...args,
	            components: new Set(),
	            schematics: new Set(),
	            processingResults: new Set(),
	            fonts: new Set(),
	            layerSetTransforms: new Set(),
	            imageExtractions: new Set(),
	            projectModules: new Set(),
	        }, args.name, new Set([
	            TransformComponent_1.TransformComponent,
	            TransformProcessingResult_1.TransformProcessingResult,
	            TransformProjectFont_1.TransformProjectFont,
	            TransformProjectLayerSet_1.TransformProjectLayerSet,
	            TransformProjectModule_1.TransformProjectModule,
	            TransformSchematic_1.TransformSchematic,
	            TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic,
	            TransformImageExtraction_1.TransformImageExtraction,
	        ]), context);
	        this.netGroups = [];
	    }
	    get resolvedNetGroups() {
	        return [...this.netGroups];
	    }
	    add(child) {
	        this.assertCanAdd(child);
	        if (child instanceof TransformProcessingResult_1.TransformProcessingResult) {
	            this._sharedData.processingResults.add(child);
	        }
	        else if (child instanceof TransformComponent_1.TransformComponent) {
	            this._sharedData.components.add(child);
	        }
	        else if (child instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	            this._sharedData.schematics.add(child);
	        }
	        else if (child instanceof TransformSchematic_1.TransformSchematic) {
	            this._sharedData.schematics.add(child);
	        }
	        else if (child instanceof TransformProjectFont_1.TransformProjectFont) {
	            this._sharedData.fonts.add(child);
	        }
	        else if (child instanceof TransformProjectLayerSet_1.TransformProjectLayerSet) {
	            this._sharedData.layerSetTransforms.add(child);
	        }
	        else if (child instanceof TransformProjectModule_1.TransformProjectModule) {
	            this._sharedData.projectModules.add(child);
	        }
	        else if (child instanceof TransformImageExtraction_1.TransformImageExtraction) {
	            this._sharedData.imageExtractions.add(child);
	        }
	    }
	    compile() {
	        const schematics = Array.from(this._sharedData.schematics).map((child) => [child.compiled.designId, child.compiled]);
	        const components = Array.from(this._sharedData.components).map((child) => [child.compiled.name, child.compiled]);
	        const fonts = Array.from(this._sharedData.fonts).map((child) => [child.compiled.name, child.compiled]);
	        const layerSets = Object.fromEntries(Array.from(this._sharedData.layerSetTransforms).map((child) => [child.compiled.id, child.compiled.layers]));
	        const processingResults = {
	            type: 'project-processing',
	            message: '',
	            innerResults: Array.from(this._sharedData.processingResults).map((child) => child.compiled),
	        };
	        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(processingResults);
	        if (processingResults.severity !== sigma_engine_shared_1.DesignModels.BoundaryLevel.Recommendation) {
	            processingResults.message = `Project processing completed with ${processingResults.severity}s`;
	        }
	        else {
	            processingResults.message = 'Project processing completed successfully';
	        }
	        this.resolvePhysicalNetNames(schematics);
	        const seenModuleNames = new Set();
	        const projectModules = [];
	        for (const pm of this._sharedData.projectModules) {
	            const compiled = pm.compiled;
	            if (!seenModuleNames.has(compiled.name)) {
	                seenModuleNames.add(compiled.name);
	                projectModules.push(compiled);
	            }
	        }
	        const assets = Array.from(this._sharedData.imageExtractions).map((ie) => ({
	            type: sigma_engine_shared_1.ProjectModelAssetType.Image,
	            ref: ie.compiled.ref,
	            name: ie.compiled.name,
	            mimeType: ie.compiled.mimeType,
	        }));
	        return (0, Font_1.packModelFonts)({
	            name: this._sharedData.name,
	            type: this._sharedData.type,
	            layerSets,
	            modules: projectModules,
	            fonts: {
	                fonts: Object.fromEntries(fonts),
	            },
	            warnings: [],
	            components: Object.fromEntries(components),
	            designs: Object.fromEntries(schematics),
	            processingResults,
	            globalNets: {},
	            ...(assets.length > 0 ? { assets } : {}),
	        });
	    }
	    resolvePhysicalNetNames(schematics) {
	        const allTransformSchematics = [];
	        for (const child of this._sharedData.schematics) {
	            if (child instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	                const multi = child;
	                for (const s of multi.childSchematics) {
	                    allTransformSchematics.push(s);
	                }
	            }
	            else if (child instanceof TransformSchematic_1.TransformSchematic) {
	                allTransformSchematics.push(child);
	            }
	        }
	        if (allTransformSchematics.length === 0) {
	            return;
	        }
	        const rootSchematics = allTransformSchematics.filter((s) => s.instance?.path?.length === 0);
	        if (rootSchematics.length === 0) {
	            return;
	        }
	        const instanceToSchematic = new Map();
	        for (const s of allTransformSchematics) {
	            if (s.instance) {
	                instanceToSchematic.set(s.instance, s);
	            }
	        }
	        const buildHierarchy = (schematic) => {
	            const instance = schematic.instance;
	            const children = [];
	            if (instance?.children) {
	                for (const childInstance of instance.children) {
	                    const childSchematic = instanceToSchematic.get(childInstance);
	                    if (childSchematic) {
	                        children.push(buildHierarchy(childSchematic));
	                    }
	                }
	            }
	            return {
	                pathUniqueId: instance?.pathUniqueId ?? '',
	                hierarchyDepth: instance?.path?.length ?? 0,
	                instance,
	                combobulator: schematic.netCombobulator,
	                children,
	            };
	        };
	        const namingStrategy = rootSchematics[0].netCombobulator?.namingStrategy;
	        if (!namingStrategy?.resolvePhysicalNetNames) {
	            return;
	        }
	        const rootHierarchies = rootSchematics.map(buildHierarchy);
	        const projectHierarchy = rootHierarchies.length === 1 ? rootHierarchies[0] : namingStrategy.createVirtualRootHierarchy(rootHierarchies);
	        const allGroups = namingStrategy.resolvePhysicalNetNames(projectHierarchy);
	        this.netGroups.length = 0;
	        this.netGroups.push(...allGroups);
	        this.applyPhysicalNetNames(allGroups, schematics, instanceToSchematic);
	    }
	    applyPhysicalNetNames(groups, schematics, instanceToSchematic) {
	        const modelsByDesignId = new Map();
	        for (const [designId, model] of schematics) {
	            modelsByDesignId.set(designId, model);
	        }
	        const multiInstanceDesignIds = new Set();
	        for (const schematic of instanceToSchematic.values()) {
	            if (!modelsByDesignId.has(schematic.designId)) {
	                const parent = schematic.parent;
	                if (parent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	                    const overviewModel = modelsByDesignId.get(parent.designId);
	                    if (overviewModel) {
	                        modelsByDesignId.set(schematic.designId, overviewModel);
	                        multiInstanceDesignIds.add(schematic.designId);
	                    }
	                }
	            }
	        }
	        const confirmedNames = new Map();
	        const claimedPins = new Map();
	        const claimedConnections = new Map();
	        const drawingNetRenames = new Map();
	        const pinIdentity = (moduleRef, pinRef) => `${moduleRef} ${pinRef}`;
	        for (const group of groups) {
	            if (!group.physicalName) {
	                continue;
	            }
	            for (const [instance, nets] of group.getNets()) {
	                const schematic = instanceToSchematic.get(instance);
	                const model = schematic ? modelsByDesignId.get(schematic.designId) : undefined;
	                if (!model) {
	                    continue;
	                }
	                const isMultiInstanceChild = schematic ? multiInstanceDesignIds.has(schematic.designId) : false;
	                const instanceDesignId = schematic?.designId;
	                const confirmed = confirmedNames.get(model) ?? new Set();
	                confirmedNames.set(model, confirmed);
	                const modelClaimedPins = claimedPins.get(model) ?? new Set();
	                claimedPins.set(model, modelClaimedPins);
	                const modelClaimedConnections = claimedConnections.get(model) ?? new Set();
	                claimedConnections.set(model, modelClaimedConnections);
	                for (const net of nets) {
	                    const localName = net.name.valueOf();
	                    if (localName === group.physicalName) {
	                        if (!isMultiInstanceChild) {
	                            confirmed.add(localName);
	                        }
	                        continue;
	                    }
	                    if (!isMultiInstanceChild && confirmed.has(localName)) {
	                        continue;
	                    }
	                    if (isMultiInstanceChild) {
	                        const instanceModuleRefs = new Set();
	                        for (const mod of model.modules) {
	                            if (mod.multiInstance?.instanceId === instanceDesignId) {
	                                instanceModuleRefs.add(mod.ref);
	                            }
	                        }
	                        this.claimAndRewriteModulePinNets(model, localName, group.physicalName, modelClaimedPins, pinIdentity, (mod) => instanceModuleRefs.has(mod.ref));
	                        this.claimAndMoveNetConnections(model, localName, group.physicalName, modelClaimedConnections, pinIdentity, (conn) => instanceModuleRefs.has(conn.moduleRef));
	                    }
	                    else {
	                        const modelRenames = drawingNetRenames.get(model) ?? new Map();
	                        if (!modelRenames.has(localName)) {
	                            modelRenames.set(localName, group.physicalName);
	                        }
	                        drawingNetRenames.set(model, modelRenames);
	                        this.claimAndMoveNetConnections(model, localName, group.physicalName, modelClaimedConnections, pinIdentity, () => true);
	                        this.claimAndRewriteModulePinNets(model, localName, group.physicalName, modelClaimedPins, pinIdentity, () => true);
	                    }
	                }
	            }
	        }
	        for (const [model, renames] of drawingNetRenames) {
	            this.applyDrawingNetRenames(model, renames);
	        }
	        const overviewModels = new Set();
	        for (const designId of multiInstanceDesignIds) {
	            const overview = modelsByDesignId.get(designId);
	            if (overview) {
	                overviewModels.add(overview);
	            }
	        }
	        const allNetNames = new Set();
	        for (const model of new Set(modelsByDesignId.values())) {
	            for (const name of Object.keys(model.nets)) {
	                allNetNames.add(name);
	            }
	        }
	        for (const model of new Set(modelsByDesignId.values())) {
	            if (overviewModels.has(model)) {
	                continue;
	            }
	            for (const [layer, drawings] of Object.entries(model.drawings)) {
	                model.drawings[layer] = drawings.map((drawing) => (0, mapDrawingNetLabels_1.mapDrawingNetLabels)(drawing, (net) => (allNetNames.has(net) ? undefined : null)));
	            }
	        }
	    }
	    applyDrawingNetRenames(model, renames) {
	        if (renames.size === 0) {
	            return;
	        }
	        for (const [layer, drawings] of Object.entries(model.drawings)) {
	            model.drawings[layer] = drawings.map((drawing) => (0, mapDrawingNetLabels_1.mapDrawingNetLabels)(drawing, (net) => renames.get(net)));
	        }
	    }
	    claimAndMoveNetConnections(model, localName, physicalName, claimedConnections, pinIdentity, isEligibleConnection) {
	        const netData = model.nets[localName];
	        if (!netData) {
	            return;
	        }
	        const movedConns = [];
	        const remainingConns = [];
	        for (const conn of netData.connections) {
	            const identity = pinIdentity(conn.moduleRef, conn.pinRef);
	            if (isEligibleConnection(conn) && !claimedConnections.has(identity)) {
	                claimedConnections.add(identity);
	                movedConns.push(conn);
	            }
	            else {
	                remainingConns.push(conn);
	            }
	        }
	        if (movedConns.length === 0) {
	            return;
	        }
	        netData.connections = remainingConns;
	        if (model.nets[physicalName]) {
	            model.nets[physicalName].connections.push(...movedConns);
	        }
	        else {
	            model.nets[physicalName] = { name: physicalName, connections: movedConns };
	        }
	        if (remainingConns.length === 0) {
	            delete model.nets[localName];
	        }
	    }
	    claimAndRewriteModulePinNets(model, localName, physicalName, claimedPins, pinIdentity, isEligibleModule) {
	        for (const mod of model.modules) {
	            if (!isEligibleModule(mod)) {
	                continue;
	            }
	            for (const [pinRef, pinNet] of Object.entries(mod.pinNets)) {
	                if (pinNet !== localName) {
	                    continue;
	                }
	                const identity = pinIdentity(mod.ref, pinRef);
	                if (claimedPins.has(identity)) {
	                    continue;
	                }
	                claimedPins.add(identity);
	                mod.pinNets[pinRef] = physicalName;
	            }
	        }
	    }
	}
	TransformProject.TransformProject = TransformProject$1;
	
	return TransformProject;
}

var OrcadDsnTransformProjectGenerator = {};

var OrcadDsnUtils = {};

var hasRequiredOrcadDsnUtils;

function requireOrcadDsnUtils () {
	if (hasRequiredOrcadDsnUtils) return OrcadDsnUtils;
	hasRequiredOrcadDsnUtils = 1;
	Object.defineProperty(OrcadDsnUtils, "__esModule", { value: true });
	OrcadDsnUtils.OrcadDsnUtils = void 0;
	OrcadDsnUtils.orcadDesignId = orcadDesignId;
	OrcadDsnUtils.isHierarchicalBlockPlacement = isHierarchicalBlockPlacement;
	OrcadDsnUtils.unresolvedBlockIsPhysicalPart = unresolvedBlockIsPhysicalPart;
	OrcadDsnUtils.placedViewSuffixOf = placedViewSuffixOf;
	const sigma_engine_shared_1 = requireDist$1();
	const Transform_1 = requireTransform();
	function orcadDesignId(canonicalName) {
	    let h = 0x811c9dc5;
	    for (let i = 0; i < canonicalName.length; i++) {
	        h ^= canonicalName.charCodeAt(i);
	        h = Math.imul(h, 0x01000193);
	    }
	    return `d_${(h >>> 0).toString(16).padStart(8, '0')}`;
	}
	function isHierarchicalBlockPlacement(partInst) {
	    return partInst.ContentsViewType === 1;
	}
	function unresolvedBlockIsPhysicalPart(expandsIntoSubSheet, design, partInst) {
	    if (!isHierarchicalBlockPlacement(partInst) || expandsIntoSubSheet) {
	        return false;
	    }
	    const footprint = partInst.UserProperty?.find((property) => property.Name === 'PCB Footprint')?.Value;
	    if (!footprint || footprint.trim().length === 0) {
	        return false;
	    }
	    return placementHasCachedPins(design, partInst);
	}
	function placedViewSuffixOf(graphicName) {
	    const match = /(\.(?:Normal|Convert|IEEE))$/i.exec(graphicName);
	    return match === null ? undefined : match[1];
	}
	function placementHasCachedPins(design, partInst) {
	    const packageResolution = OrcadDsnUtils$1.findBestPackageForPartInst(design.Cache, partInst);
	    if (!packageResolution) {
	        return false;
	    }
	    const placedViewSuffix = placedViewSuffixOf(partInst.GraphicName);
	    let anyViewHasPins = false;
	    for (const libPart of packageResolution.pkg.LibPart) {
	        if (!libPart.CellName || !OrcadDsnUtils$1.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
	            continue;
	        }
	        for (const view of [...(libPart.NormalView ?? []), ...(libPart.ConvertView ?? [])]) {
	            const hasPins = (view.PinScalar?.length ?? 0) > 0 || (view.PinBus?.length ?? 0) > 0;
	            if (view.Suffix === placedViewSuffix) {
	                return hasPins;
	            }
	            if (hasPins) {
	                anyViewHasPins = true;
	            }
	        }
	    }
	    return placedViewSuffix === undefined && anyViewHasPins;
	}
	class OrcadDsnUtils$1 {
	    static findPackageByIdentity(cache, sourceLibName, packageName, packageIndex) {
	        const packages = cache?.Package;
	        if (!packages) {
	            return undefined;
	        }
	        if (packageIndex !== undefined) {
	            const pkg = packages[packageIndex];
	            if (pkg?.SourceLibName === sourceLibName && pkg.Name === packageName) {
	                return pkg;
	            }
	        }
	        return this.findLastPackageByIdentity(cache, sourceLibName, packageName);
	    }
	    static findLastPackageByIdentity(cache, sourceLibName, packageName) {
	        const packages = cache?.Package;
	        if (!packages) {
	            return undefined;
	        }
	        for (let i = packages.length - 1; i >= 0; i--) {
	            const pkg = packages[i];
	            if (pkg.SourceLibName === sourceLibName && pkg.Name === packageName) {
	                return pkg;
	            }
	        }
	        return undefined;
	    }
	    static findBestPackageForPartInst(cache, partInst) {
	        const packages = cache?.Package;
	        if (!packages) {
	            return undefined;
	        }
	        let lastMatchingPackage;
	        let bestCompatiblePackage;
	        for (let packageIndex = 0; packageIndex < packages.length; packageIndex++) {
	            const pkg = packages[packageIndex];
	            if (pkg.SourceLibName !== partInst.LibName || pkg.Name !== partInst.PkgName) {
	                continue;
	            }
	            lastMatchingPackage = { pkg, packageIndex };
	            const score = this.scorePackageCompatibility(pkg, partInst);
	            if (score === undefined) {
	                continue;
	            }
	            if (!bestCompatiblePackage || score > bestCompatiblePackage.score) {
	                bestCompatiblePackage = { pkg, packageIndex, score };
	            }
	        }
	        return bestCompatiblePackage ?? lastMatchingPackage;
	    }
	    static scorePackageCompatibility(pkg, partInst) {
	        let bestScore;
	        for (const libPart of pkg.LibPart) {
	            if (!libPart.CellName || !OrcadDsnUtils$1.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
	                continue;
	            }
	            for (const normalView of libPart.NormalView ?? []) {
	                const score = this.scoreNormalViewCompatibility(normalView, partInst);
	                if (score === undefined) {
	                    continue;
	                }
	                if (bestScore === undefined || score > bestScore) {
	                    bestScore = score;
	                }
	            }
	        }
	        return bestScore;
	    }
	    static scoreNormalViewCompatibility(normalView, partInst) {
	        const ports = [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])];
	        if (ports.length === 0) {
	            return 0;
	        }
	        const pinsByPosition = new Map();
	        for (const pin of [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])]) {
	            pinsByPosition.set(pin.Position, pin);
	        }
	        let score = 0;
	        for (const port of ports) {
	            const pin = pinsByPosition.get(port.Position);
	            if (!pin) {
	                return undefined;
	            }
	            if (pin.Name !== port.Name) {
	                return undefined;
	            }
	            if (pin.HotptX !== port.HotptX ||
	                pin.HotptY !== port.HotptY ||
	                pin.StartX !== port.StartX ||
	                pin.StartY !== port.StartY) {
	                return undefined;
	            }
	            score += 100;
	            if (pin.Type === port.Type) {
	                score += 10;
	            }
	            if (Boolean(pin.Visible) === port.Visible) {
	                score += 1;
	            }
	        }
	        return score;
	    }
	    static getRefSchematicPrefix(relativeTo, targetFile, schematic) {
	        const path = OrcadDsnUtils$1.resolveFilePath(relativeTo, targetFile);
	        return `${path}::${schematic}`;
	    }
	    static resolveFilePath(relativeToFile, targetFile) {
	        const normalizedTarget = targetFile?.replace(/\\/g, '/');
	        return sigma_engine_shared_1.Files.resolvePath(sigma_engine_shared_1.Files.dirnamePath(relativeToFile.replace(/\\/g, '/').replace(/^\.\\/, '')), normalizedTarget ?? sigma_engine_shared_1.Files.basenamePath(relativeToFile));
	    }
	    static getRefViewName(relativeTo, targetFile, schematic, page) {
	        return `${OrcadDsnUtils$1.getRefSchematicPrefix(relativeTo, targetFile, schematic)}::${page}`;
	    }
	    static isRefForDesignSchematic(ref, sourceFile, schematic) {
	        return ref.startsWith(`${sourceFile}::${schematic}::`);
	    }
	    static getTitleBlockUserProp(tb, name) {
	        return (0, sigma_engine_shared_1.toArray)(tb)
	            .map((t) => t.UserProperty?.find((p) => p.Name === name))
	            .find((v) => v !== undefined);
	    }
	    static graphicNameMatchesCellName(graphicName, cellName) {
	        if (!graphicName.startsWith(cellName)) {
	            return false;
	        }
	        return graphicName.length === cellName.length || graphicName[cellName.length] === '.';
	    }
	    static orcadRotationToCadstrom(rotation, mirror) {
	        return {
	            angle: rotation * 90,
	            mirrored: mirror === 1 ? [rotation === 1 || rotation === 3, rotation === 2 || rotation === 0] : [false, false],
	        };
	    }
	    static computeModuleCenter(inst, boundingBox) {
	        const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
	        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
	        let center = pointTransform([inst.LocX, inst.LocY]);
	        if (inst.Rotation % 2 === 1) {
	            center = oneWeirdTrick(center);
	        }
	        return center;
	    }
	    static transformPinToSchematicCoords(symbolPin, partInst, boundingBox) {
	        const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
	        let pin = [symbolPin.HotptX - bboxCenter[0], symbolPin.HotptY - bboxCenter[1]];
	        const rot = partInst.Rotation % 4;
	        if (rot === 1) {
	            pin = [pin[1], -pin[0]];
	        }
	        else if (rot === 2) {
	            pin = [-pin[0], -pin[1]];
	        }
	        else if (rot === 3) {
	            pin = [-pin[1], pin[0]];
	        }
	        const { mirrored } = OrcadDsnUtils$1.orcadRotationToCadstrom(partInst.Rotation, partInst.Mirror);
	        if (mirrored[1]) {
	            pin = [-pin[0], pin[1]];
	        }
	        if (mirrored[0]) {
	            pin = [pin[0], -pin[1]];
	        }
	        const moduleCenter = OrcadDsnUtils$1.computeModuleCenter(partInst, boundingBox);
	        return [moduleCenter[0] + pin[0], moduleCenter[1] + pin[1]];
	    }
	}
	OrcadDsnUtils.OrcadDsnUtils = OrcadDsnUtils$1;
	
	return OrcadDsnUtils;
}

var orcadLayers = {};

var hasRequiredOrcadLayers;

function requireOrcadLayers () {
	if (hasRequiredOrcadLayers) return orcadLayers;
	hasRequiredOrcadLayers = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OrCadSchLayers = exports.OrCadSchLayerMap = exports.ORCAD_SCH_LAYERSET = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const { SchLayerType } = sigma_engine_shared_1.DesignModels;
		exports.ORCAD_SCH_LAYERSET = 'orcad-sch';
		exports.OrCadSchLayerMap = {
		    [SchLayerType.Label]: { id: 1, type: SchLayerType.Label, name: 'Label' },
		    [SchLayerType.NameLabel]: { id: 2, type: SchLayerType.NameLabel, name: 'NameLabel' },
		    [SchLayerType.ValueLabel]: { id: 3, type: SchLayerType.ValueLabel, name: 'ValueLabel' },
		    [SchLayerType.Pin]: { id: 4, type: SchLayerType.Pin, name: 'Pin' },
		    [SchLayerType.PinName]: { id: 5, type: SchLayerType.PinName, name: 'PinName' },
		    [SchLayerType.PinNumber]: { id: 6, type: SchLayerType.PinNumber, name: 'PinNumber' },
		    [SchLayerType.Symbol]: { id: 7, type: SchLayerType.Symbol, name: 'Symbol' },
		    [SchLayerType.Junction]: { id: 8, type: SchLayerType.Junction, name: 'Junction' },
		    [SchLayerType.BusJunction]: { id: 9, type: SchLayerType.BusJunction, name: 'BusJunction' },
		    [SchLayerType.Wire]: { id: 10, type: SchLayerType.Wire, name: 'Wire' },
		    [SchLayerType.Bus]: { id: 11, type: SchLayerType.Bus, name: 'Bus' },
		    [SchLayerType.Other]: { id: 12, type: SchLayerType.Other, name: 'Other' },
		    [SchLayerType.Background]: { id: 13, type: SchLayerType.Background, name: 'Background Contrast' },
		    [SchLayerType.Image]: { id: 14, type: SchLayerType.Image, name: 'Image' },
		};
		exports.OrCadSchLayers = Object.values(exports.OrCadSchLayerMap);
		
	} (orcadLayers));
	return orcadLayers;
}

var orcadLogicalSchematicReferenceResolver = {};

var hasRequiredOrcadLogicalSchematicReferenceResolver;

function requireOrcadLogicalSchematicReferenceResolver () {
	if (hasRequiredOrcadLogicalSchematicReferenceResolver) return orcadLogicalSchematicReferenceResolver;
	hasRequiredOrcadLogicalSchematicReferenceResolver = 1;
	Object.defineProperty(orcadLogicalSchematicReferenceResolver, "__esModule", { value: true });
	orcadLogicalSchematicReferenceResolver.normalizeOrCadLogicalSchematicPath = normalizeOrCadLogicalSchematicPath;
	orcadLogicalSchematicReferenceResolver.resolveOrCadLogicalSchematicReference = resolveOrCadLogicalSchematicReference;
	const sigma_engine_shared_1 = requireDist$1();
	function normalizeSeparators(path) {
	    return path.replace(/\\/g, '/');
	}
	function normalizeOrCadLogicalSchematicPath(path) {
	    return normalizeSeparators(sigma_engine_shared_1.Files.resolvePath('.', normalizeSeparators(path))).toLowerCase();
	}
	function canonicalSchematicStem(path) {
	    return normalizeOrCadLogicalSchematicPath(path).replace(/\.(dsn|xml)$/i, '');
	}
	function isAbsoluteOrCadPath(path) {
	    return path.startsWith('/') || path.startsWith('//') || /^[a-z]:\//i.test(path);
	}
	function normalizeReference(referencingFile, reference) {
	    const normalizedReference = normalizeSeparators(reference);
	    const allowSuffixMatch = isAbsoluteOrCadPath(normalizedReference);
	    const baseDirectory = normalizeSeparators(sigma_engine_shared_1.Files.dirnamePath(normalizeSeparators(referencingFile)));
	    const path = normalizeSeparators(sigma_engine_shared_1.Files.resolvePath(allowSuffixMatch ? '' : baseDirectory, normalizedReference)).toLowerCase();
	    return {
	        path,
	        stem: path.replace(/\.(dsn|xml)$/i, ''),
	        allowSuffixMatch,
	    };
	}
	function candidatePreference(sourceFile) {
	    if (/\.dsn$/i.test(sourceFile)) {
	        return 2;
	    }
	    if (/\.xml$/i.test(sourceFile)) {
	        return 1;
	    }
	    return 0;
	}
	function groupedCandidates(candidates, referencingFile) {
	    const referencingStem = canonicalSchematicStem(referencingFile);
	    const grouped = new Map();
	    for (const candidate of candidates) {
	        const stem = canonicalSchematicStem(candidate.sourceFile);
	        if (stem === referencingStem) {
	            continue;
	        }
	        const selected = grouped.get(stem);
	        if (!selected || candidatePreference(candidate.sourceFile) > candidatePreference(selected.sourceFile)) {
	            grouped.set(stem, candidate);
	        }
	    }
	    return Array.from(grouped, ([stem, candidate]) => ({ stem, candidate })).sort((a, b) => a.stem.localeCompare(b.stem));
	}
	function resolutionFromMatches(matches, normalizedPath, matchKind) {
	    if (matches.length === 1) {
	        return { status: 'resolved', candidate: matches[0].candidate, matchKind, normalizedPath };
	    }
	    if (matches.length > 1) {
	        return {
	            status: 'unresolved',
	            reason: 'ambiguous',
	            normalizedPath,
	            candidates: matches.map((match) => match.candidate.sourceFile),
	        };
	    }
	    return undefined;
	}
	function resolveOrCadLogicalSchematicReference(candidates, referencingFile, reference, referencedViewName) {
	    const normalizedReference = normalizeReference(referencingFile, reference);
	    const grouped = groupedCandidates(candidates, referencingFile);
	    const exact = resolutionFromMatches(grouped.filter((candidate) => candidate.stem === normalizedReference.stem), normalizedReference.path, 'exact');
	    if (exact) {
	        return exact;
	    }
	    if (normalizedReference.allowSuffixMatch) {
	        const suffix = resolutionFromMatches(grouped.filter((candidate) => normalizedReference.stem.endsWith(candidate.stem)), normalizedReference.path, 'suffix');
	        if (suffix) {
	            return suffix;
	        }
	    }
	    const referencedBasename = sigma_engine_shared_1.Files.basenamePath(normalizedReference.stem).toLowerCase();
	    const basename = resolutionFromMatches(grouped.filter((candidate) => sigma_engine_shared_1.Files.basenamePath(candidate.stem).toLowerCase() === referencedBasename), normalizedReference.path, 'basename');
	    if (basename) {
	        return basename;
	    }
	    const normalizedViewName = referencedViewName?.trim().toLowerCase();
	    if (normalizedViewName) {
	        const view = resolutionFromMatches(grouped.filter((candidate) => candidate.candidate.schematicViewNames?.some((viewName) => viewName.trim().toLowerCase() === normalizedViewName)), normalizedReference.path, 'view');
	        if (view) {
	            return view;
	        }
	    }
	    return {
	        status: 'unresolved',
	        reason: 'missing',
	        normalizedPath: normalizedReference.path,
	        candidates: [],
	    };
	}
	
	return orcadLogicalSchematicReferenceResolver;
}

var hasRequiredOrcadDsnTransformProjectGenerator;

function requireOrcadDsnTransformProjectGenerator () {
	if (hasRequiredOrcadDsnTransformProjectGenerator) return OrcadDsnTransformProjectGenerator;
	hasRequiredOrcadDsnTransformProjectGenerator = 1;
	Object.defineProperty(OrcadDsnTransformProjectGenerator, "__esModule", { value: true });
	OrcadDsnTransformProjectGenerator.OrCADDsnTransformProjectGenerator = void 0;
	const TransformProject_1 = requireTransformProject();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const orcadValidation_1 = requireOrcadValidation();
	const sigma_engine_shared_1 = requireDist$1();
	const sigma_engine_shared_2 = requireDist$1();
	const TransformProjectLayerSet_1 = requireTransformProjectLayerSet();
	const orcadLayers_1 = requireOrcadLayers();
	const orcadLogicalSchematicReferenceResolver_1 = requireOrcadLogicalSchematicReferenceResolver();
	function compareByCodeUnit(left, right) {
	    if (left === right) {
	        return 0;
	    }
	    return left < right ? -1 : 1;
	}
	class OrCADDsnTransformProjectGenerator {
	    constructor() {
	        this.runsFor = null;
	    }
	    *generate(_trigger, rootContext) {
	        const contexts = this.detectRootDesign(rootContext, this.detectLogicalSchemas(rootContext));
	        for (const context of contexts) {
	            const project = new TransformProject_1.TransformProject({
	                name: 'OrCAD DSN Project',
	                type: 'orcad',
	            }, context);
	            yield project;
	            for (const message of context.unresolvedHierarchyReferences ?? []) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
	                    type: 'orcad-hierarchy-reference-unresolved',
	                    severity: sigma_engine_shared_2.BoundaryLevel.Warning,
	                    message,
	                });
	            }
	            for (const message of context.ambiguousRootRequests ?? []) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
	                    type: 'orcad-root-request-ambiguous',
	                    severity: sigma_engine_shared_2.BoundaryLevel.Warning,
	                    message,
	                });
	            }
	            yield new TransformProjectLayerSet_1.TransformProjectLayerSet(project, {
	                type: 'layerSet',
	                id: orcadLayers_1.ORCAD_SCH_LAYERSET,
	                layerSetType: 'schematic',
	                layers: orcadLayers_1.OrCadSchLayers.map((layer) => ({
	                    ...layer,
	                    defaultVisible: typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true,
	                })),
	            });
	        }
	    }
	    detectLogicalSchemas(rootContext) {
	        const schemas = new Map();
	        for (const [sourceFile, descr] of Object.entries(rootContext.parsedDef.schematics)) {
	            if (!descr.validationResult?.isValid ||
	                !descr.validationResult.data?.metadata?.[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema]) {
	                continue;
	            }
	            const schema = descr.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
	            schemas.set(sourceFile, schema);
	        }
	        return schemas;
	    }
	    detectRootDesign(rootContext, logicalSchemas) {
	        const schematicHierarchies = [];
	        rootContext.unresolvedHierarchyReferences = [];
	        const pageChildren = new Map();
	        const designSourceFiles = new Map();
	        for (const [sourceFile, design] of logicalSchemas) {
	            designSourceFiles.set(design, sourceFile);
	        }
	        const requestedSourceFile = rootContext.options.requestedSourceFile;
	        const requestedMatches = requestedSourceFile === undefined ?
	            []
	            : Array.from(logicalSchemas.entries())
	                .filter(([sourceFile]) => (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(sourceFile) ===
	                (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(requestedSourceFile))
	                .sort(([left], [right]) => compareByCodeUnit(left, right));
	        const requestedDesign = requestedMatches[0]?.[1];
	        if (requestedSourceFile !== undefined) {
	            if (requestedDesign === undefined) {
	                throw new Error(`Requested OrCAD source file was not found: ${requestedSourceFile}`);
	            }
	            const matchedSourceFiles = requestedMatches.map(([sourceFile]) => sourceFile);
	            if (matchedSourceFiles.length > 1) {
	                rootContext.ambiguousRootRequests = [
	                    `Requested OrCAD source file "${requestedSourceFile}" matched multiple OrCAD source files (${matchedSourceFiles.join(', ')}); rooted the project at ${matchedSourceFiles[0]}.`,
	                ];
	            }
	        }
	        const hierarchyRefs = new Map();
	        const logicalSchematicCandidates = Array.from(logicalSchemas, ([sourceFile, design]) => ({
	            sourceFile,
	            schematicViewNames: design.Schematic.map((schematic) => schematic.Name),
	        }));
	        const resolveTargetSourceFile = (sourceFile, targetFile, targetViewName) => {
	            if (!targetFile) {
	                return sourceFile;
	            }
	            const sourceStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(sourceFile).replace(/\.(dsn|xml)$/i, '');
	            const targetStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(sourceFile, targetFile)).replace(/\.(dsn|xml)$/i, '');
	            if (sourceStem === targetStem) {
	                return sourceFile;
	            }
	            const resolution = (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(logicalSchematicCandidates, sourceFile, targetFile, targetViewName);
	            return resolution.status === 'resolved' ? resolution.candidate.sourceFile : undefined;
	        };
	        for (const [sourceFile, design] of logicalSchemas) {
	            for (const schematic of design.Schematic) {
	                for (const page of schematic.Page) {
	                    const schematicHierarchy = {
	                        name: OrcadDsnUtils_1.OrcadDsnUtils.getRefViewName(sourceFile, undefined, schematic.Name, page.Name),
	                        sourceFile: OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(sourceFile),
	                        schematicIndex: 0,
	                        children: [],
	                        parents: [],
	                        hierarchyIndex: 0,
	                        design,
	                        schematic,
	                        page,
	                        slug: `${sigma_engine_shared_1.Files.basenamePath(sourceFile)}_${schematic.Name}_${page.Name}`.replace(/[^a-zA-Z0-9._-]/g, '_'),
	                    };
	                    const pageChildrenArr = [];
	                    pageChildren.set(schematicHierarchy, pageChildrenArr);
	                    schematicHierarchies.push(schematicHierarchy);
	                    for (const inst of page.DrawnInst ?? []) {
	                        if (inst.ContentsLibName || inst.ContentsViewName) {
	                            const targetSourceFile = resolveTargetSourceFile(sourceFile, inst.ContentsLibName || undefined, inst.ContentsViewName);
	                            if (!targetSourceFile) {
	                                rootContext.unresolvedHierarchyReferences.push(`Referenced schematic could not be resolved: ${inst.ContentsLibName ?? inst.ContentsViewName ?? inst.Name}`);
	                                continue;
	                            }
	                            const targetFile = OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(targetSourceFile);
	                            const viewName = OrcadDsnUtils_1.OrcadDsnUtils.getRefSchematicPrefix(targetSourceFile, undefined, inst.ContentsViewName);
	                            const arr = hierarchyRefs.get(viewName) ?? [];
	                            arr.push(schematicHierarchy);
	                            hierarchyRefs.set(viewName, arr);
	                            pageChildrenArr.push({
	                                name: `${viewName}::${inst.Name}`,
	                                sheetSymbol: inst,
	                                targetFile,
	                                targetSchematic: inst.ContentsViewName,
	                            });
	                        }
	                    }
	                    for (const inst of page.PartInst ?? []) {
	                        const targetSourceFile = resolveTargetSourceFile(sourceFile, inst.ContentsLibName || undefined, inst.ContentsViewName);
	                        if (inst.ContentsViewType === 1) {
	                            if (!targetSourceFile) {
	                                rootContext.unresolvedHierarchyReferences.push(`Referenced schematic could not be resolved: ${inst.ContentsLibName ?? inst.ContentsViewName ?? inst.Name}`);
	                                continue;
	                            }
	                            const targetFile = OrcadDsnUtils_1.OrcadDsnUtils.resolveFilePath(targetSourceFile);
	                            const viewName = OrcadDsnUtils_1.OrcadDsnUtils.getRefSchematicPrefix(targetSourceFile, undefined, inst.ContentsViewName);
	                            const arr = hierarchyRefs.get(viewName) ?? [];
	                            arr.push(schematicHierarchy);
	                            hierarchyRefs.set(viewName, arr);
	                            pageChildrenArr.push({
	                                name: `${viewName}::${inst.Name}`,
	                                sheetSymbol: inst,
	                                targetFile,
	                                targetSchematic: inst.ContentsViewName,
	                            });
	                        }
	                    }
	                }
	            }
	        }
	        for (const [hierarchyName, hierarchyEntries] of Array.from(hierarchyRefs)) {
	            if (hierarchyEntries.length === 0) {
	                continue;
	            }
	            const normalizedHierarchyName = hierarchyName.toLowerCase();
	            const sourceFileHierarchies = schematicHierarchies.filter((hierarchy) => hierarchy.name.toLowerCase().startsWith(`${normalizedHierarchyName}::`));
	            if (!sourceFileHierarchies.length) {
	                for (const parent of hierarchyEntries) {
	                    rootContext.unresolvedHierarchyReferences.push(`Referenced schematic view was not found: ${hierarchyName} (from ${parent.name})`);
	                }
	                continue;
	            }
	            for (const parent of hierarchyEntries) {
	                for (const sourceFileHierarchy of sourceFileHierarchies) {
	                    parent.children.push(sourceFileHierarchy);
	                    sourceFileHierarchy.parents.push(parent);
	                }
	            }
	            hierarchyRefs.delete(hierarchyName);
	        }
	        const rootDesigns = new Map();
	        for (const hierarchy of schematicHierarchies) {
	            if (requestedDesign && hierarchy.design !== requestedDesign) {
	                continue;
	            }
	            if (!requestedDesign && hierarchy.parents.length > 0) {
	                continue;
	            }
	            if (hierarchy.schematic.Name !== hierarchy.design.RootName) {
	                continue;
	            }
	            const designArr = rootDesigns.get(hierarchy.design) ?? [];
	            designArr.push(hierarchy);
	            rootDesigns.set(hierarchy.design, designArr);
	        }
	        const orderedRootDesigns = Array.from(rootDesigns.entries()).sort(([left], [right]) => compareByCodeUnit(designSourceFiles.get(left) ?? left.Name, designSourceFiles.get(right) ?? right.Name));
	        rootContext.detectedRootSourceFiles = orderedRootDesigns.map(([design]) => designSourceFiles.get(design) ?? design.Name);
	        return orderedRootDesigns.map(([rootDesign, rootHierarchies]) => {
	            const rootSchematic = rootDesign.Schematic.find((s) => s.Name === rootDesign.RootName);
	            if (!rootSchematic) {
	                throw new Error(`Root schematic not found`);
	            }
	            const hierarchyPortDiagnostics = new Set();
	            rootContext.hierarchyPortDiagnosticSets = rootContext.hierarchyPortDiagnosticSets ?? [];
	            rootContext.hierarchyPortDiagnosticSets.push(hierarchyPortDiagnostics);
	            return {
	                ...rootContext,
	                hierarchyPortDiagnostics,
	                rootDesign: rootDesign,
	                rootSchematic,
	                rootHierarchies,
	                allHierarchies: schematicHierarchies,
	                pageChildren,
	                designSourceFiles,
	                instanceSchematicMap: new Map(),
	            };
	        });
	    }
	}
	OrcadDsnTransformProjectGenerator.OrCADDsnTransformProjectGenerator = OrCADDsnTransformProjectGenerator;
	
	return OrcadDsnTransformProjectGenerator;
}

var OrcadDsnTransformSchematicGenerator = {};

var OrcadNetNamingStrategy = {};

var OrcadGlobalSignalGroupingRule = {};

var hasRequiredOrcadGlobalSignalGroupingRule;

function requireOrcadGlobalSignalGroupingRule () {
	if (hasRequiredOrcadGlobalSignalGroupingRule) return OrcadGlobalSignalGroupingRule;
	hasRequiredOrcadGlobalSignalGroupingRule = 1;
	Object.defineProperty(OrcadGlobalSignalGroupingRule, "__esModule", { value: true });
	OrcadGlobalSignalGroupingRule.OrCadGlobalSignalGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrCadGlobalSignalGroupingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        const scopedNames = this.collectPowerPortScopedNames(manager);
	        const globalGroups = new Map();
	        for (const group of manager.iterateGroups()) {
	            for (const label of group.getNetLabels()) {
	                if (label.labelType === 'global') {
	                    const name = (label.formattedLabel ?? label.originalLabel).toUpperCase();
	                    if (scopedNames.has(name)) {
	                        continue;
	                    }
	                    if (!globalGroups.has(name)) {
	                        globalGroups.set(name, new Set());
	                    }
	                    globalGroups.get(name).add(group);
	                }
	            }
	            for (const [, nets] of group.getNets()) {
	                for (const net of nets) {
	                    for (const conn of net.connections) {
	                        if (conn.connection.appliesNetName?.labelType === 'global') {
	                            const name = conn.connection.appliesNetName.label.toUpperCase();
	                            if (scopedNames.has(name)) {
	                                continue;
	                            }
	                            if (!globalGroups.has(name)) {
	                                globalGroups.set(name, new Set());
	                            }
	                            globalGroups.get(name).add(group);
	                        }
	                    }
	                }
	            }
	        }
	        for (const groups of globalGroups.values()) {
	            if (groups.size <= 1) {
	                continue;
	            }
	            manager.mergeGroups(...Array.from(groups));
	        }
	        return manager;
	    }
	    collectPowerPortScopedNames(manager) {
	        const scoped = new Set();
	        for (const group of manager.iterateGroups()) {
	            for (const label of group.getNetLabels()) {
	                if (label.labelType !== 'hierarchical-port') {
	                    continue;
	                }
	                scoped.add((label.formattedLabel ?? label.originalLabel).toUpperCase());
	            }
	        }
	        return scoped;
	    }
	}
	OrcadGlobalSignalGroupingRule.OrCadGlobalSignalGroupingRule = OrCadGlobalSignalGroupingRule;
	
	return OrcadGlobalSignalGroupingRule;
}

var OrcadHierarchicalPortGroupingRule = {};

var OrcadDsnPortOccurrenceUtils = {};

var OrcadDsnBusUtils = {};

var hasRequiredOrcadDsnBusUtils;

function requireOrcadDsnBusUtils () {
	if (hasRequiredOrcadDsnBusUtils) return OrcadDsnBusUtils;
	hasRequiredOrcadDsnBusUtils = 1;
	Object.defineProperty(OrcadDsnBusUtils, "__esModule", { value: true });
	OrcadDsnBusUtils.busDimensionName = busDimensionName;
	OrcadDsnBusUtils.busPositionalDimensionName = busPositionalDimensionName;
	OrcadDsnBusUtils.parseBusMemberCount = parseBusMemberCount;
	OrcadDsnBusUtils.getBusDetails = getBusDetails;
	OrcadDsnBusUtils.getNetBusMemberDimensions = getNetBusMemberDimensions;
	OrcadDsnBusUtils.getNetBusMemberDimensionsByName = getNetBusMemberDimensionsByName;
	OrcadDsnBusUtils.inferNetBusName = inferNetBusName;
	OrcadDsnBusUtils.findNetBusMemberDimensions = findNetBusMemberDimensions;
	OrcadDsnBusUtils.buildBusPositionLookup = buildBusPositionLookup;
	OrcadDsnBusUtils.busPortAlignsWithParent = busPortAlignsWithParent;
	function busDimensionName(memberDim) {
	    return `bus${memberDim.index}`;
	}
	function busPositionalDimensionName(positionalIndex) {
	    return `bus${positionalIndex}-pos`;
	}
	function extractBusPrefix(busName) {
	    const idx = busName.indexOf('[');
	    return idx > 0 ? busName.substring(0, idx) : undefined;
	}
	function parseBusMemberCount(busName) {
	    const match = busName.match(/\[(\d+)(?:\.\.|:)(\d+)\]/);
	    if (!match) {
	        return undefined;
	    }
	    return Math.abs(parseInt(match[2], 10) - parseInt(match[1], 10)) + 1;
	}
	function getBusDetails(busPortName) {
	    const BusNameRegex = /^(.+)\[(\d+)(?:\.\.|:)(\d+)\]$/;
	    const match = busPortName.match(BusNameRegex);
	    if (!match) {
	        return undefined;
	    }
	    const prefix = match[1];
	    const start = parseInt(match[2], 10);
	    const end = parseInt(match[3], 10);
	    const count = Math.abs(end - start) + 1;
	    const step = end >= start ? 1 : -1;
	    return { match, count, start, step, prefix };
	}
	function getNetBusMemberDimensions(netBus) {
	    return (netBus.NetScalar ?? [])
	        .map((ns) => ns.Name)
	        .filter(Boolean)
	        .map((name, index) => ({ name, index }));
	}
	function getNetBusMemberDimensionsByName(netBus, busName) {
	    const fallback = getNetBusMemberDimensions(netBus);
	    if (busName) {
	        const details = getBusDetails(busName);
	        if (details) {
	            const byName = tryIndexByPrefix(netBus, details.prefix, details.start, details.step);
	            if (byName) {
	                return byName;
	            }
	        }
	    }
	    const autoDetected = tryAutoDetectPrefix(netBus);
	    if (autoDetected) {
	        return autoDetected;
	    }
	    return fallback;
	}
	function tryIndexByPrefix(netBus, prefix, _start, _step) {
	    const memberRegex = new RegExp(`^${escapeRegex(prefix)}(\\d+)$`);
	    const result = [];
	    for (const ns of netBus.NetScalar ?? []) {
	        if (!ns.Name) {
	            continue;
	        }
	        const match = ns.Name.match(memberRegex);
	        if (!match) {
	            return undefined;
	        }
	        const parsed = parseInt(match[1], 10);
	        if (parsed < 0) {
	            return undefined;
	        }
	        result.push({ name: ns.Name, index: parsed });
	    }
	    return result;
	}
	function tryAutoDetectPrefix(netBus) {
	    const names = (netBus.NetScalar ?? []).map((ns) => ns.Name).filter((n) => Boolean(n));
	    if (names.length === 0) {
	        return undefined;
	    }
	    const splitRegex = /^(.+?)(\d+)$/;
	    const parts = [];
	    for (const name of names) {
	        const m = name.match(splitRegex);
	        if (!m) {
	            return undefined;
	        }
	        parts.push({ name, prefix: m[1], digit: parseInt(m[2], 10) });
	    }
	    const commonPrefix = parts[0].prefix;
	    if (!parts.every((p) => p.prefix === commonPrefix)) {
	        return undefined;
	    }
	    return parts.map((p) => ({ name: p.name, index: p.digit }));
	}
	function escapeRegex(s) {
	    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}
	function inferNetBusName(netBus) {
	    if (netBus.Name && getBusDetails(netBus.Name)) {
	        return netBus.Name;
	    }
	    for (const wireBus of netBus.WireBus ?? []) {
	        for (const alias of wireBus.Alias ?? []) {
	            if (alias.Name && getBusDetails(alias.Name)) {
	                return alias.Name;
	            }
	        }
	    }
	    return undefined;
	}
	function findNetBusMemberDimensions(page, busName) {
	    for (const netBus of page.NetBus ?? []) {
	        const aliasMatch = (netBus.WireBus ?? []).some((w) => (w.Alias ?? []).some((a) => a.Name === busName));
	        if (aliasMatch || netBus.Name === busName) {
	            return getNetBusMemberDimensions(netBus);
	        }
	    }
	    const prefix = extractBusPrefix(busName);
	    const expectedCount = parseBusMemberCount(busName);
	    if (prefix) {
	        for (const netBus of page.NetBus ?? []) {
	            const members = (netBus.NetScalar ?? []).map((ns) => ns.Name).filter(Boolean);
	            if (members.length > 0 &&
	                (!expectedCount || members.length === expectedCount) &&
	                members.every((name) => name.startsWith(prefix))) {
	                return getNetBusMemberDimensions(netBus);
	            }
	        }
	    }
	    return undefined;
	}
	function buildBusPositionLookup(page) {
	    const map = new Map();
	    for (const netBus of page.NetBus ?? []) {
	        const name = inferNetBusName(netBus);
	        const members = getNetBusMemberDimensionsByName(netBus, name);
	        if (members.length === 0) {
	            continue;
	        }
	        for (const wireBus of netBus.WireBus ?? []) {
	            map.set(`${wireBus.StartX},${wireBus.StartY}`, { members, name });
	            map.set(`${wireBus.EndX},${wireBus.EndY}`, { members, name });
	        }
	    }
	    return (positionKey) => map.get(positionKey);
	}
	function busPortAlignsWithParent(portMembers, parentMembers, parentName) {
	    if (portMembers.length !== parentMembers.length) {
	        return false;
	    }
	    const details = parentName ? getBusDetails(parentName) : undefined;
	    if (!details) {
	        const parentBits = new Set(parentMembers.map((m) => m.index));
	        return portMembers.every((m) => parentBits.has(m.index));
	    }
	    return portMembers.every((m, i) => m.index === details.start + i * details.step);
	}
	
	return OrcadDsnBusUtils;
}

var hasRequiredOrcadDsnPortOccurrenceUtils;

function requireOrcadDsnPortOccurrenceUtils () {
	if (hasRequiredOrcadDsnPortOccurrenceUtils) return OrcadDsnPortOccurrenceUtils;
	hasRequiredOrcadDsnPortOccurrenceUtils = 1;
	Object.defineProperty(OrcadDsnPortOccurrenceUtils, "__esModule", { value: true });
	OrcadDsnPortOccurrenceUtils.occurrenceBindingForDeclaredBusMember = occurrenceBindingForDeclaredBusMember;
	OrcadDsnPortOccurrenceUtils.coversCompleteDeclaredBusRange = coversCompleteDeclaredBusRange;
	OrcadDsnPortOccurrenceUtils.busPortMembersFromOccurrenceBindings = busPortMembersFromOccurrenceBindings;
	OrcadDsnPortOccurrenceUtils.parentBasePortOccurrenceIdentity = parentBasePortOccurrenceIdentity;
	OrcadDsnPortOccurrenceUtils.parentPortOccurrenceBindings = parentPortOccurrenceBindings;
	OrcadDsnPortOccurrenceUtils.childPortOccurrenceBindings = childPortOccurrenceBindings;
	OrcadDsnPortOccurrenceUtils.childPortMemberOccurrenceBindings = childPortMemberOccurrenceBindings;
	OrcadDsnPortOccurrenceUtils.portOccurrenceIdentityKey = portOccurrenceIdentityKey;
	const OrcadDsnBusUtils_1 = requireOrcadDsnBusUtils();
	const portOccurrenceNormalization_1 = requirePortOccurrenceNormalization();
	function occurrenceBindingForDeclaredBusMember(bindings, declarationOrdinal, literalBitIndex, declaredMemberCount) {
	    const isCompleteAndAligned = bindings.length === declaredMemberCount &&
	        bindings.every((binding, ordinal) => binding.identity.declarationOrdinal === ordinal);
	    return isCompleteAndAligned ?
	        bindings[declarationOrdinal]
	        : bindings.find((binding) => binding.identity.literalBitIndex === literalBitIndex);
	}
	function coversCompleteDeclaredBusRange(declaredPortName, bindings) {
	    const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
	    if (declaredMembers.length === 0 || bindings.length !== declaredMembers.length) {
	        return false;
	    }
	    const declaredIndices = new Set(declaredMembers.map((member) => member.index));
	    const bindingIndices = bindings.map((binding) => binding.identity.literalBitIndex);
	    return (bindingIndices.every((index) => index !== undefined) &&
	        new Set(bindingIndices).size === bindingIndices.length &&
	        bindingIndices.every((index) => declaredIndices.has(index)));
	}
	function busPortMembersFromOccurrenceBindings(declaredPortName, bindings) {
	    const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
	    if (!coversCompleteDeclaredBusRange(declaredPortName, bindings)) {
	        return declaredMembers;
	    }
	    return bindings.map((binding) => ({
	        name: binding.parentMemberName ?? binding.childPortName,
	        index: binding.identity.literalBitIndex,
	    }));
	}
	function valueKey(value) {
	    return String(value).toUpperCase();
	}
	function isMemberOccurrence(occurrence) {
	    return occurrence.RefBusMemberName !== '';
	}
	function literalBitIndex(name) {
	    const match = name.match(/(\d+)$/);
	    return match ? parseInt(match[1], 10) : undefined;
	}
	function declarationOrdinalForMember(occurrences, member, fallbackOrdinal) {
	    const bases = occurrences.filter((row) => !isMemberOccurrence(row) && valueKey(row.RefKey) === valueKey(member.RefKey));
	    if (bases.length !== 1) {
	        return fallbackOrdinal;
	    }
	    const ordinal = (0, portOccurrenceNormalization_1.expandBusPortName)(String(bases[0].RefPortName)).findIndex((declared) => valueKey(declared.name) === valueKey(member.RefBusMemberName));
	    return ordinal >= 0 ? ordinal : fallbackOrdinal;
	}
	function boundaryChild(parent, sheetSymbolDbId, reportDiagnostic) {
	    const children = parent.children.filter((child) => 'instOccurence' in child && child.sheetSymbol?.DbId.toString() === sheetSymbolDbId);
	    const paths = new Set(children.map((child) => child.pathUniqueId));
	    const occurrences = new Set(children.map((child) => child.instOccurence));
	    if (children.length === 0 || paths.size !== 1 || occurrences.size !== 1) {
	        reportDiagnostic(`[hierarchy-port-boundary-ambiguous] parent=${parent.pathUniqueId} sheetSymbol=${sheetSymbolDbId} children=${children.length}`);
	        return undefined;
	    }
	    return children[0];
	}
	function baseRowsForKey(occurrences, portKey) {
	    const requestedKey = valueKey(portKey);
	    const keyed = occurrences.filter((occurrence) => !isMemberOccurrence(occurrence) && valueKey(occurrence.RefKey) === requestedKey);
	    return keyed.length > 0 ?
	        keyed
	        : occurrences.filter((occurrence) => !isMemberOccurrence(occurrence) && valueKey(occurrence.RefPortName) === requestedKey);
	}
	function childDeclarationNames(children) {
	    const ports = children.flatMap((child) => child.hierarchyEntry.page?.Port ?? []);
	    const netMembers = children.flatMap((child) => (child.hierarchyEntry.page?.NetBus ?? []).flatMap((bus) => (bus.NetScalar ?? []).map((member) => member.Name)));
	    if (ports.length === 0 && netMembers.length === 0) {
	        return undefined;
	    }
	    const names = new Set();
	    for (const port of ports) {
	        names.add(valueKey(port.Name));
	        for (const member of (0, portOccurrenceNormalization_1.expandBusPortName)(port.Name)) {
	            names.add(valueKey(member.name));
	        }
	    }
	    for (const memberName of netMembers) {
	        names.add(valueKey(memberName));
	    }
	    return names;
	}
	function hasAmbiguousMemberForBase(base, occurrences) {
	    const names = new Set((0, portOccurrenceNormalization_1.expandBusPortName)(String(base.RefPortName)).map((member) => valueKey(member.name)));
	    return occurrences.some((row) => row.KeyType === 'AmbiguousPortMember' && isMemberOccurrence(row) && names.has(valueKey(row.RefBusMemberName)));
	}
	function parentBasePortOccurrenceIdentity(parent, sheetSymbolDbId, portKey, reportDiagnostic = () => undefined) {
	    const child = boundaryChild(parent, sheetSymbolDbId, reportDiagnostic);
	    if (!child) {
	        return undefined;
	    }
	    const bases = baseRowsForKey(child.instOccurence.PortOccurrence ?? [], portKey);
	    if (bases.length !== 1) {
	        reportDiagnostic(`[hierarchy-port-unresolved] boundary=${child.pathUniqueId} port=${String(portKey)} bases=${bases.length}`);
	        return undefined;
	    }
	    return { boundaryPath: child.pathUniqueId, refKey: valueKey(bases[0].RefKey) };
	}
	function parentPortOccurrenceBindings(parent, sheetSymbolDbId, portKey, declaredPortName, reportDiagnostic = () => undefined) {
	    const child = boundaryChild(parent, sheetSymbolDbId, reportDiagnostic);
	    if (!child) {
	        return [];
	    }
	    const occurrences = child.instOccurence.PortOccurrence ?? [];
	    const bases = baseRowsForKey(occurrences, portKey);
	    if (bases.length !== 1) {
	        reportDiagnostic(`[hierarchy-port-unresolved] boundary=${child.pathUniqueId} port=${declaredPortName} bases=${bases.length}`);
	        return [];
	    }
	    const base = bases[0];
	    const refKey = valueKey(base.RefKey);
	    const members = occurrences.filter((occurrence) => isMemberOccurrence(occurrence) && valueKey(occurrence.RefKey) === refKey);
	    if (members.length === 0 && hasAmbiguousMemberForBase(base, occurrences)) {
	        reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${child.pathUniqueId} port=${declaredPortName} reason=ambiguous-member-owner`);
	        return [];
	    }
	    if (members.length > 0) {
	        const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
	        const declaredByName = new Map(declaredMembers.map((member, ordinal) => [valueKey(member.name), { member, ordinal }]));
	        const physicalParentNames = new Set((parent.hierarchyEntry.page ?
	            ((0, OrcadDsnBusUtils_1.findNetBusMemberDimensions)(parent.hierarchyEntry.page, declaredPortName) ?? [])
	            : []).map((member) => valueKey(member.name)));
	        const boundaryPages = parent.children.filter((candidate) => 'instOccurence' in candidate &&
	            candidate.pathUniqueId === child.pathUniqueId &&
	            candidate.instOccurence === child.instOccurence);
	        const childNames = childDeclarationNames(boundaryPages);
	        const invalidParent = members.filter((member) => declaredMembers.length > 0 &&
	            !declaredByName.has(valueKey(member.RefBusMemberName)) &&
	            !physicalParentNames.has(valueKey(member.RefBusMemberName)));
	        const invalidChild = members.filter((member) => childNames !== undefined && !childNames.has(valueKey(member.RefPortName)));
	        if (invalidParent.length > 0 || invalidChild.length > 0) {
	            reportDiagnostic(`[hierarchy-port-unresolved] boundary=${child.pathUniqueId} port=${declaredPortName} reason=invalid-explicit-members parent=${invalidParent[0]?.RefBusMemberName ?? '-'} child=${invalidChild[0]?.RefPortName ?? '-'}`);
	            return [];
	        }
	        const bindings = members.map((member, fallbackOrdinal) => {
	            const declared = declaredByName.get(valueKey(member.RefBusMemberName));
	            return {
	                identity: {
	                    boundaryPath: child.pathUniqueId,
	                    refKey,
	                    declarationOrdinal: declared?.ordinal ?? declarationOrdinalForMember(occurrences, member, fallbackOrdinal),
	                    literalBitIndex: declared?.member.index ?? literalBitIndex(member.RefBusMemberName),
	                },
	                parentMemberName: member.RefBusMemberName,
	                childPortName: String(member.RefPortName),
	            };
	        });
	        const ordered = declaredMembers.flatMap((declaredMember) => {
	            const match = bindings.find((binding) => valueKey(binding.parentMemberName ?? '') === valueKey(declaredMember.name));
	            return match ? [match] : [];
	        });
	        return ordered.length === bindings.length ? ordered : bindings;
	    }
	    const baseMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(String(base.RefPortName));
	    const compatibilityMembers = baseMembers.length > 0 ? baseMembers : (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
	    if (compatibilityMembers.length > 0) {
	        return compatibilityMembers.map((member, declarationOrdinal) => ({
	            identity: { boundaryPath: child.pathUniqueId, refKey, declarationOrdinal, literalBitIndex: member.index },
	            parentMemberName: member.name,
	            childPortName: member.name,
	        }));
	    }
	    return [{ identity: { boundaryPath: child.pathUniqueId, refKey }, childPortName: String(base.RefPortName) }];
	}
	function childPortOccurrenceBindings(instance, declaredPortName, reportDiagnostic = () => undefined) {
	    if (instance.path.length === 0) {
	        return [];
	    }
	    const occurrences = instance.instOccurence.PortOccurrence ?? [];
	    const memberRows = occurrences.filter((occurrence) => isMemberOccurrence(occurrence) && valueKey(occurrence.RefPortName) === valueKey(declaredPortName));
	    const explicitBindings = memberRows.flatMap((memberRow) => {
	        const siblings = occurrences.filter((occurrence) => isMemberOccurrence(occurrence) && valueKey(occurrence.RefKey) === valueKey(memberRow.RefKey));
	        const siblingOrdinal = siblings.indexOf(memberRow);
	        const declarationOrdinal = declarationOrdinalForMember(occurrences, memberRow, siblingOrdinal);
	        return declarationOrdinal < 0 ?
	            []
	            : [
	                {
	                    identity: {
	                        boundaryPath: instance.pathUniqueId,
	                        refKey: valueKey(memberRow.RefKey),
	                        declarationOrdinal,
	                        literalBitIndex: literalBitIndex(memberRow.RefBusMemberName),
	                    },
	                    memberName: memberRow.RefBusMemberName,
	                },
	            ];
	    });
	    if (explicitBindings.length === 1) {
	        return explicitBindings;
	    }
	    if (explicitBindings.length > 1) {
	        reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${instance.pathUniqueId} port=${declaredPortName} explicit=${explicitBindings.length}`);
	        return [];
	    }
	    const baseRows = occurrences.filter((occurrence) => !isMemberOccurrence(occurrence) && valueKey(occurrence.RefPortName) === valueKey(declaredPortName));
	    if (baseRows.length === 1) {
	        return [{ identity: { boundaryPath: instance.pathUniqueId, refKey: valueKey(baseRows[0].RefKey) } }];
	    }
	    const compatible = [];
	    for (const base of occurrences.filter((occurrence) => !isMemberOccurrence(occurrence))) {
	        const refKey = valueKey(base.RefKey);
	        if (occurrences.some((row) => isMemberOccurrence(row) && valueKey(row.RefKey) === refKey) ||
	            hasAmbiguousMemberForBase(base, occurrences)) {
	            continue;
	        }
	        const members = (0, portOccurrenceNormalization_1.expandBusPortName)(String(base.RefPortName));
	        const declarationOrdinal = members.findIndex((member) => valueKey(member.name) === valueKey(declaredPortName));
	        if (declarationOrdinal >= 0) {
	            compatible.push({
	                identity: {
	                    boundaryPath: instance.pathUniqueId,
	                    refKey,
	                    declarationOrdinal,
	                    literalBitIndex: members[declarationOrdinal].index,
	                },
	                memberName: members[declarationOrdinal].name,
	            });
	        }
	    }
	    if (compatible.length !== 1 && compatible.length > 0) {
	        reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${instance.pathUniqueId} port=${declaredPortName} compatible=${compatible.length}`);
	    }
	    if (compatible.length === 0) {
	        reportDiagnostic(`[hierarchy-port-unresolved] boundary=${instance.pathUniqueId} port=${declaredPortName} reason=no-occurrence-binding`);
	    }
	    return compatible.length === 1 ? compatible : [];
	}
	function childPortMemberOccurrenceBindings(instance, declaredPortName, reportDiagnostic = () => undefined) {
	    const occurrences = instance.instOccurence.PortOccurrence ?? [];
	    const bases = occurrences.filter((row) => !isMemberOccurrence(row) && valueKey(row.RefPortName) === valueKey(declaredPortName));
	    if (bases.length !== 1) {
	        reportDiagnostic(bases.length > 1 ?
	            `[hierarchy-port-ambiguous] boundary=${instance.pathUniqueId} port=${declaredPortName} bases=${bases.length}`
	            : `[hierarchy-port-unresolved] boundary=${instance.pathUniqueId} port=${declaredPortName} bases=0`);
	        return [];
	    }
	    const refKey = valueKey(bases[0].RefKey);
	    const members = occurrences.filter((row) => isMemberOccurrence(row) && valueKey(row.RefKey) === refKey);
	    return members.map((member, fallbackOrdinal) => ({
	        identity: {
	            boundaryPath: instance.pathUniqueId,
	            refKey,
	            declarationOrdinal: declarationOrdinalForMember(occurrences, member, fallbackOrdinal),
	            literalBitIndex: literalBitIndex(member.RefBusMemberName),
	        },
	        memberName: String(member.RefPortName),
	    }));
	}
	function portOccurrenceIdentityKey(identity) {
	    return `${identity.boundaryPath}\u0000${identity.refKey}\u0000${identity.declarationOrdinal ?? 'base'}\u0000${identity.literalBitIndex ?? 'scalar'}`;
	}
	
	return OrcadDsnPortOccurrenceUtils;
}

var hasRequiredOrcadHierarchicalPortGroupingRule;

function requireOrcadHierarchicalPortGroupingRule () {
	if (hasRequiredOrcadHierarchicalPortGroupingRule) return OrcadHierarchicalPortGroupingRule;
	hasRequiredOrcadHierarchicalPortGroupingRule = 1;
	Object.defineProperty(OrcadHierarchicalPortGroupingRule, "__esModule", { value: true });
	OrcadHierarchicalPortGroupingRule.OrCadHierarchicalPortGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const OrcadDsnPortOccurrenceUtils_1 = requireOrcadDsnPortOccurrenceUtils();
	class OrCadHierarchicalPortGroupingRule {
	    constructor(reportDiagnostic = () => undefined) {
	        this.reportDiagnostic = reportDiagnostic;
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, hierarchy) {
	        this.mergeHierarchicalPorts(manager, hierarchy);
	        return manager;
	    }
	    mergeHierarchicalPorts(manager, hierarchy) {
	        const emitDiagnostics = hierarchy.instance.pathUniqueId !== '__virtual_root__';
	        const childrenByBoundary = new Map();
	        for (const child of hierarchy.children) {
	            const boundaryPath = child.instance.pathUniqueId;
	            const boundaryChildren = childrenByBoundary.get(boundaryPath) ?? [];
	            boundaryChildren.push(child);
	            childrenByBoundary.set(boundaryPath, boundaryChildren);
	        }
	        for (const [boundaryPath, boundaryChildren] of childrenByBoundary) {
	            const distinctPages = new Set(boundaryChildren.map((boundaryChild) => boundaryChild.instance.hierarchyEntry));
	            const occurrenceNodes = new Set(boundaryChildren.map((boundaryChild) => boundaryChild.instance.instOccurence));
	            const isValidatedMultiPageOccurrence = boundaryChildren.length > 1 && distinctPages.size === boundaryChildren.length && occurrenceNodes.size === 1;
	            if (boundaryChildren.length !== 1 && !isValidatedMultiPageOccurrence) {
	                if (emitDiagnostics) {
	                    this.reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${boundaryPath} hierarchyNodes=${boundaryChildren.length}`);
	                }
	                for (const boundaryChild of boundaryChildren) {
	                    this.mergeHierarchicalPorts(manager, boundaryChild);
	                }
	                continue;
	            }
	            const child = boundaryChildren[0];
	            const parentPortGroups = manager.findGroupsWithLabel('hierarchical-sheet-entry', undefined, hierarchy.instance);
	            const childPortGroups = boundaryChildren.flatMap((boundaryChild) => manager.findGroupsWithLabel('hierarchical-port', undefined, boundaryChild.instance));
	            const parentByIdentity = this.groupsByOccurrence(parentPortGroups, hierarchy.instance, 'hierarchical-sheet-entry', child.instance.sheetSymbol?.DbId.toString());
	            const childByIdentity = new Map();
	            for (const boundaryChild of boundaryChildren) {
	                this.addGroupsByOccurrence(childByIdentity, manager.findGroupsWithLabel('hierarchical-port', undefined, boundaryChild.instance), boundaryChild.instance, 'hierarchical-port');
	            }
	            if (emitDiagnostics &&
	                (parentPortGroups.length > 0 || childPortGroups.length > 0) &&
	                (parentByIdentity.size === 0 || childByIdentity.size === 0)) {
	                this.reportDiagnostic(`[hierarchy-port-unresolved] boundary=${boundaryPath} parentOccurrences=${parentByIdentity.size} childOccurrences=${childByIdentity.size}`);
	            }
	            const identityKeys = new Set([...parentByIdentity.keys(), ...childByIdentity.keys()]);
	            const pairs = [];
	            for (const identityKey of identityKeys) {
	                const parentGroups = (parentByIdentity.get(identityKey) ?? []).filter((group) => this.hasWireOrPhysicalPin(group));
	                const childGroups = (childByIdentity.get(identityKey) ?? []).filter((group) => this.hasWireOrPhysicalPin(group));
	                if (parentGroups.length === 0 && childGroups.length === 0) {
	                    continue;
	                }
	                if (parentGroups.length === 0 || childGroups.length === 0) {
	                    if (emitDiagnostics) {
	                        this.reportDiagnostic(`[hierarchy-port-unresolved] boundary=${boundaryPath} identity=${identityKey} parent=${parentGroups.length} child=${childGroups.length}`);
	                    }
	                    continue;
	                }
	                const childNames = this.occurrenceLabelNames(childGroups, 'hierarchical-port', identityKey);
	                if (parentGroups.length !== 1 || childNames.size !== 1) {
	                    if (emitDiagnostics) {
	                        this.reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${boundaryPath} identity=${identityKey} parent=${parentGroups.length} child=${childGroups.length}`);
	                    }
	                    continue;
	                }
	                pairs.push([parentGroups[0], childGroups]);
	            }
	            for (const [parentGroup, childGroups] of pairs) {
	                manager.mergeGroups(parentGroup, ...childGroups.filter((childGroup) => childGroup !== parentGroup));
	            }
	            for (const boundaryChild of boundaryChildren) {
	                this.mergeHierarchicalPorts(manager, boundaryChild);
	            }
	        }
	    }
	    occurrenceLabelNames(groups, labelType, identityKey) {
	        const names = new Set();
	        for (const group of groups) {
	            for (const label of group.getNetLabels()) {
	                const occurrence = label.strategyMetadata?.['port-occurrence'];
	                if (label.labelType === labelType && occurrence && (0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence) === identityKey) {
	                    names.add(label.originalLabel.toUpperCase());
	                }
	            }
	        }
	        return names;
	    }
	    hasWireOrPhysicalPin(group) {
	        for (const [, nets] of group.getNets()) {
	            for (const net of nets) {
	                if (net.connections.length > 0 || net.netObjects.some((object) => object.wire !== undefined)) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    groupsByOccurrence(groups, instance, labelType, sheetSymbolDbId) {
	        const result = new Map();
	        this.addGroupsByOccurrence(result, groups, instance, labelType, sheetSymbolDbId);
	        return result;
	    }
	    addGroupsByOccurrence(result, groups, instance, labelType, sheetSymbolDbId) {
	        for (const group of groups) {
	            for (const label of group.getNetLabels(instance)) {
	                if (label.labelType !== labelType) {
	                    continue;
	                }
	                if (labelType === 'hierarchical-sheet-entry' &&
	                    label.strategyMetadata?.['hierarchical-sheet-entry']?.sheetSymbolDbId !== sheetSymbolDbId) {
	                    continue;
	                }
	                const occurrence = label.strategyMetadata?.['port-occurrence'];
	                if (!occurrence) {
	                    continue;
	                }
	                const key = (0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence);
	                const entries = result.get(key) ?? [];
	                if (!entries.includes(group)) {
	                    entries.push(group);
	                    result.set(key, entries);
	                }
	            }
	        }
	    }
	}
	OrcadHierarchicalPortGroupingRule.OrCadHierarchicalPortGroupingRule = OrCadHierarchicalPortGroupingRule;
	
	return OrcadHierarchicalPortGroupingRule;
}

var OrcadBusMemberGroupingRule = {};

var hasRequiredOrcadBusMemberGroupingRule;

function requireOrcadBusMemberGroupingRule () {
	if (hasRequiredOrcadBusMemberGroupingRule) return OrcadBusMemberGroupingRule;
	hasRequiredOrcadBusMemberGroupingRule = 1;
	Object.defineProperty(OrcadBusMemberGroupingRule, "__esModule", { value: true });
	OrcadBusMemberGroupingRule.OrcadBusMemberGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrcadBusMemberGroupingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        const busGroups = manager.findGroupsWithObjectsOnDimension('bus');
	        for (const busGroup of busGroups) {
	            this.mergeBusMembers(manager, busGroup);
	        }
	        return manager;
	    }
	    mergeBusMembers(manager, busGroup) {
	        const membersByIndex = new Map();
	        for (const [instance, nets] of busGroup.getNets()) {
	            let membersFound = false;
	            for (const net of nets) {
	                if (membersFound) {
	                    break;
	                }
	                for (const obj of net.netObjects) {
	                    const busMetadata = obj.wire?.strategyMetadata?.bus;
	                    if (busMetadata?.members) {
	                        for (const member of busMetadata.members) {
	                            const entries = membersByIndex.get(member.index) ?? [];
	                            entries.push({ instance, memberName: member.name });
	                            membersByIndex.set(member.index, entries);
	                        }
	                        membersFound = true;
	                        break;
	                    }
	                }
	            }
	        }
	        for (const [_index, entries] of membersByIndex) {
	            const entriesByInstance = new Map();
	            for (const entry of entries) {
	                const localEntries = entriesByInstance.get(entry.instance) ?? [];
	                localEntries.push(entry);
	                entriesByInstance.set(entry.instance, localEntries);
	            }
	            for (const localEntries of entriesByInstance.values()) {
	                this.mergeLocalMembers(manager, localEntries);
	            }
	        }
	    }
	    mergeLocalMembers(manager, entries) {
	        if (entries.length < 2) {
	            return;
	        }
	        const groupsToMerge = [];
	        const seen = new Set();
	        for (const { instance, memberName } of entries) {
	            const matchingGroups = manager
	                .findGroupsWithLabel('alias', memberName, instance)
	                .concat(manager.findGroupsWithLabel('virtual-bus-member', memberName, instance));
	            for (const group of matchingGroups) {
	                if (group.hasObjectsOnDimensionMatching((dimension) => dimension.endsWith('-pos'))) {
	                    continue;
	                }
	                if (!seen.has(group)) {
	                    seen.add(group);
	                    groupsToMerge.push(group);
	                }
	            }
	        }
	        if (groupsToMerge.length >= 2) {
	            manager.mergeGroups(...groupsToMerge);
	        }
	    }
	}
	OrcadBusMemberGroupingRule.OrcadBusMemberGroupingRule = OrcadBusMemberGroupingRule$1;
	
	return OrcadBusMemberGroupingRule;
}

var OrcadBusMemberNamingRule = {};

var hasRequiredOrcadBusMemberNamingRule;

function requireOrcadBusMemberNamingRule () {
	if (hasRequiredOrcadBusMemberNamingRule) return OrcadBusMemberNamingRule;
	hasRequiredOrcadBusMemberNamingRule = 1;
	Object.defineProperty(OrcadBusMemberNamingRule, "__esModule", { value: true });
	OrcadBusMemberNamingRule.OrcadBusMemberNamingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrcadBusMemberNamingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, hierarchy) {
	        const instanceBusMembers = new Map();
	        const instanceDepth = new Map();
	        for (const [instance, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const wire of net.wires) {
	                    const metadata = wire.strategyMetadata?.['bus-member'];
	                    if (metadata) {
	                        const busMembers = instanceBusMembers.get(instance) ?? [];
	                        busMembers.push({
	                            name: metadata.memberName,
	                            index: metadata.memberIndex,
	                        });
	                        instanceBusMembers.set(instance, busMembers);
	                        instanceDepth.set(instance, instance.path.length);
	                    }
	                }
	            }
	        }
	        if (instanceBusMembers.size === 0) {
	            return undefined;
	        }
	        const sortedInstances = Array.from(instanceBusMembers.keys()).sort((a, b) => (instanceDepth.get(a) ?? 0) - (instanceDepth.get(b) ?? 0));
	        const shallowestInstance = sortedInstances[0];
	        const shallowestMembers = instanceBusMembers.get(shallowestInstance) ?? [];
	        return shallowestMembers[0].name;
	    }
	}
	OrcadBusMemberNamingRule.OrcadBusMemberNamingRule = OrcadBusMemberNamingRule$1;
	
	return OrcadBusMemberNamingRule;
}

var OrcadNetScalarGroupingRule = {};

var hasRequiredOrcadNetScalarGroupingRule;

function requireOrcadNetScalarGroupingRule () {
	if (hasRequiredOrcadNetScalarGroupingRule) return OrcadNetScalarGroupingRule;
	hasRequiredOrcadNetScalarGroupingRule = 1;
	Object.defineProperty(OrcadNetScalarGroupingRule, "__esModule", { value: true });
	OrcadNetScalarGroupingRule.OrCadNetScalarGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrCadNetScalarGroupingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        const buckets = new Map();
	        for (const group of manager.iterateGroups()) {
	            for (const [instance, nets] of group.getNets()) {
	                let byInstance = buckets.get(instance);
	                if (!byInstance) {
	                    byInstance = new Map();
	                    buckets.set(instance, byInstance);
	                }
	                for (const net of nets) {
	                    for (const wire of net.wires) {
	                        const id = wire.strategyMetadata?.['net-scalar-id'];
	                        if (id === undefined) {
	                            continue;
	                        }
	                        let bucket = byInstance.get(id);
	                        if (!bucket) {
	                            bucket = new Set();
	                            byInstance.set(id, bucket);
	                        }
	                        bucket.add(group);
	                    }
	                }
	            }
	        }
	        for (const byInstance of buckets.values()) {
	            for (const bucket of byInstance.values()) {
	                if (bucket.size <= 1) {
	                    continue;
	                }
	                manager.mergeGroups(...Array.from(bucket));
	            }
	        }
	        return manager;
	    }
	}
	OrcadNetScalarGroupingRule.OrCadNetScalarGroupingRule = OrCadNetScalarGroupingRule;
	
	return OrcadNetScalarGroupingRule;
}

var OrcadOffPageConnectorGroupingRule = {};

var hasRequiredOrcadOffPageConnectorGroupingRule;

function requireOrcadOffPageConnectorGroupingRule () {
	if (hasRequiredOrcadOffPageConnectorGroupingRule) return OrcadOffPageConnectorGroupingRule;
	hasRequiredOrcadOffPageConnectorGroupingRule = 1;
	Object.defineProperty(OrcadOffPageConnectorGroupingRule, "__esModule", { value: true });
	OrcadOffPageConnectorGroupingRule.OrCadOffPageConnectorGroupingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrCadOffPageConnectorGroupingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	        this.objectIds = new WeakMap();
	        this.nextObjectId = 1;
	    }
	    apply(manager, hierarchy) {
	        this.mergeOffPageConnectors(manager, hierarchy);
	        return manager;
	    }
	    mergeOffPageConnectors(manager, hierarchy) {
	        this.bucketAndMergeOffPagesAcross(manager, [hierarchy.instance]);
	        const childrenByFolder = new Map();
	        for (const child of hierarchy.children) {
	            const key = this.folderKey(child.instance);
	            const list = childrenByFolder.get(key) ?? [];
	            list.push(child);
	            childrenByFolder.set(key, list);
	        }
	        for (const siblings of childrenByFolder.values()) {
	            if (siblings.length < 2) {
	                continue;
	            }
	            this.bucketAndMergeOffPagesAcross(manager, siblings.map((s) => s.instance));
	        }
	        for (const child of hierarchy.children) {
	            this.mergeOffPageConnectors(manager, child);
	        }
	    }
	    bucketAndMergeOffPagesAcross(manager, instances) {
	        const offPageBuckets = new Map();
	        const hierarchicalPortBuckets = new Map();
	        const aliasBuckets = new Map();
	        for (const instance of instances) {
	            for (const group of manager.findGroupsWithLabel('off-page-connector', undefined, instance)) {
	                for (const label of group.getNetLabels(instance)) {
	                    if (label.labelType !== 'off-page-connector') {
	                        continue;
	                    }
	                    const name = label.formattedLabel ?? label.originalLabel;
	                    if (!offPageBuckets.has(name)) {
	                        offPageBuckets.set(name, new Set());
	                    }
	                    offPageBuckets.get(name).add(group);
	                }
	            }
	            for (const group of manager.findGroupsWithLabel('hierarchical-port', undefined, instance)) {
	                for (const label of group.getNetLabels(instance)) {
	                    if (label.labelType !== 'hierarchical-port') {
	                        continue;
	                    }
	                    const name = label.formattedLabel ?? label.originalLabel;
	                    if (!hierarchicalPortBuckets.has(name)) {
	                        hierarchicalPortBuckets.set(name, new Set());
	                    }
	                    hierarchicalPortBuckets.get(name).add(group);
	                }
	            }
	            for (const group of manager.findGroupsWithLabel('alias', undefined, instance)) {
	                for (const label of group.getNetLabels(instance)) {
	                    if (label.labelType !== 'alias') {
	                        continue;
	                    }
	                    const name = label.formattedLabel ?? label.originalLabel;
	                    const bucket = aliasBuckets.get(name) ?? new Set();
	                    bucket.add(group);
	                    aliasBuckets.set(name, bucket);
	                }
	            }
	        }
	        for (const [name, offPageGroups] of offPageBuckets) {
	            const matchingHierarchicalPortGroups = hierarchicalPortBuckets.get(name);
	            const combined = new Set(offPageGroups);
	            for (const group of aliasBuckets.get(name) ?? []) {
	                const aliasInstances = [...group.getNets().keys()];
	                if (!aliasInstances.some((instance) => [...offPageGroups].some((offPage) => offPage.hasLabelType('off-page-connector', instance)))) {
	                    continue;
	                }
	                combined.add(group);
	            }
	            if (matchingHierarchicalPortGroups) {
	                for (const g of matchingHierarchicalPortGroups) {
	                    combined.add(g);
	                }
	            }
	            if (combined.size <= 1) {
	                continue;
	            }
	            manager.mergeGroups(...Array.from(combined));
	        }
	    }
	    folderKey(instance) {
	        const design = instance.hierarchyEntry.design;
	        const schematic = instance.hierarchyEntry.schematic;
	        return `${this.objectId(design)}::${this.objectId(schematic)}::${instance.pathUniqueId}`;
	    }
	    objectId(obj) {
	        let id = this.objectIds.get(obj);
	        if (id === undefined) {
	            id = this.nextObjectId++;
	            this.objectIds.set(obj, id);
	        }
	        return id;
	    }
	}
	OrcadOffPageConnectorGroupingRule.OrCadOffPageConnectorGroupingRule = OrCadOffPageConnectorGroupingRule;
	
	return OrcadOffPageConnectorGroupingRule;
}

var OrcadGlobalSignalNamingRule = {};

var orcadHighestLevelWins = {};

var hasRequiredOrcadHighestLevelWins;

function requireOrcadHighestLevelWins () {
	if (hasRequiredOrcadHighestLevelWins) return orcadHighestLevelWins;
	hasRequiredOrcadHighestLevelWins = 1;
	Object.defineProperty(orcadHighestLevelWins, "__esModule", { value: true });
	orcadHighestLevelWins.deferToShallowerSystemId = deferToShallowerSystemId;
	function shallowestSystemIdDepth(group) {
	    let shallowest;
	    for (const [instance, nets] of group.getNets()) {
	        const depth = instance.path.length;
	        if (shallowest !== undefined && depth >= shallowest) {
	            continue;
	        }
	        for (const net of nets) {
	            if (net.wires.some((w) => w.strategyMetadata?.['system-id'])) {
	                shallowest = depth;
	                break;
	            }
	        }
	    }
	    return shallowest;
	}
	function deferToShallowerSystemId(group, labelDepth) {
	    if (labelDepth === undefined) {
	        return false;
	    }
	    const systemIdDepth = shallowestSystemIdDepth(group);
	    return systemIdDepth !== undefined && systemIdDepth < labelDepth;
	}
	
	return orcadHighestLevelWins;
}

var hasRequiredOrcadGlobalSignalNamingRule;

function requireOrcadGlobalSignalNamingRule () {
	if (hasRequiredOrcadGlobalSignalNamingRule) return OrcadGlobalSignalNamingRule;
	hasRequiredOrcadGlobalSignalNamingRule = 1;
	Object.defineProperty(OrcadGlobalSignalNamingRule, "__esModule", { value: true });
	OrcadGlobalSignalNamingRule.OrCadGlobalSignalNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	const OrcadNetNamingStrategy_1 = requireOrcadNetNamingStrategy();
	const orcadHighestLevelWins_1 = requireOrcadHighestLevelWins();
	const OrcadDsnPortOccurrenceUtils_1 = requireOrcadDsnPortOccurrenceUtils();
	class OrCadGlobalSignalNamingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        const candidates = [];
	        let shallowestAliasDepth;
	        let shallowestOffPageDepth;
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType === 'global') {
	                        candidates.push({
	                            name: label.formattedLabel ?? label.originalLabel,
	                            depth,
	                        });
	                    }
	                    else if (label.labelType === 'alias') {
	                        if (shallowestAliasDepth === undefined || depth < shallowestAliasDepth) {
	                            shallowestAliasDepth = depth;
	                        }
	                    }
	                    else if (label.labelType === 'off-page-connector') {
	                        if (shallowestOffPageDepth === undefined || depth < shallowestOffPageDepth) {
	                            shallowestOffPageDepth = depth;
	                        }
	                    }
	                }
	                for (const conn of net.connections) {
	                    if (conn.connection.appliesNetName?.labelType === 'global') {
	                        candidates.push({
	                            name: conn.connection.appliesNetName.label.toUpperCase(),
	                            depth,
	                        });
	                    }
	                }
	            }
	        }
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        candidates.sort((a, b) => {
	            if (a.depth !== b.depth) {
	                return a.depth - b.depth;
	            }
	            return sigma_engine_shared_1.Utils.naturalStringSort(a.name, b.name);
	        });
	        const shallowestGlobalDepth = candidates[0].depth;
	        if (shallowestOffPageDepth !== undefined && shallowestOffPageDepth <= shallowestGlobalDepth) {
	            return undefined;
	        }
	        if (this.crossesOccurrenceBoundary(group)) {
	            const boundaryAlias = this.shallowestBoundaryAlias(group, shallowestGlobalDepth);
	            if (boundaryAlias) {
	                return boundaryAlias;
	            }
	            if (this.hasShallowerNamedSegment(group, shallowestGlobalDepth)) {
	                return undefined;
	            }
	        }
	        if (shallowestAliasDepth !== undefined && shallowestAliasDepth < shallowestGlobalDepth) {
	            return undefined;
	        }
	        if ((0, orcadHighestLevelWins_1.deferToShallowerSystemId)(group, shallowestGlobalDepth)) {
	            return undefined;
	        }
	        return candidates[0].name;
	    }
	    crossesOccurrenceBoundary(group) {
	        const parentKeys = new Set();
	        const childKeys = new Set();
	        for (const label of group.getNetLabels()) {
	            const occurrence = label.strategyMetadata?.['port-occurrence'];
	            if (!occurrence) {
	                continue;
	            }
	            if (label.labelType === 'hierarchical-sheet-entry') {
	                parentKeys.add((0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence));
	            }
	            else if (label.labelType === 'hierarchical-port') {
	                childKeys.add((0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence));
	            }
	        }
	        return Array.from(parentKeys).some((key) => childKeys.has(key));
	    }
	    hasShallowerNamedSegment(group, globalDepth) {
	        for (const [instance, nets] of group.getNets()) {
	            if (instance.path.length >= globalDepth) {
	                continue;
	            }
	            for (const net of nets) {
	                if (net.name && !net.name.startsWith(OrcadNetNamingStrategy_1.OrCadNetNamingStrategy.AUTO_NET_PREFIX)) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    shallowestBoundaryAlias(group, globalDepth) {
	        const candidates = [];
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            if (depth >= globalDepth) {
	                continue;
	            }
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType === 'alias') {
	                        candidates.push({ name: label.formattedLabel ?? label.originalLabel, depth });
	                    }
	                }
	            }
	        }
	        candidates.sort((left, right) => left.depth - right.depth || sigma_engine_shared_1.Utils.naturalStringSort(left.name, right.name));
	        return candidates[0]?.name;
	    }
	}
	OrcadGlobalSignalNamingRule.OrCadGlobalSignalNamingRule = OrCadGlobalSignalNamingRule;
	
	return OrcadGlobalSignalNamingRule;
}

var OrcadOffPageConnectorNamingRule = {};

var hasRequiredOrcadOffPageConnectorNamingRule;

function requireOrcadOffPageConnectorNamingRule () {
	if (hasRequiredOrcadOffPageConnectorNamingRule) return OrcadOffPageConnectorNamingRule;
	hasRequiredOrcadOffPageConnectorNamingRule = 1;
	Object.defineProperty(OrcadOffPageConnectorNamingRule, "__esModule", { value: true });
	OrcadOffPageConnectorNamingRule.OrCadOffPageConnectorNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	class OrCadOffPageConnectorNamingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        if (!group.hasLabelType('off-page-connector')) {
	            return undefined;
	        }
	        const candidates = [];
	        let shallowestAliasDepth;
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            for (const net of nets) {
	                for (const label of net.labels) {
	                    if (label.labelType === 'off-page-connector') {
	                        candidates.push({
	                            name: label.formattedLabel ?? label.originalLabel,
	                            depth,
	                        });
	                    }
	                    else if (label.labelType === 'alias') {
	                        if (shallowestAliasDepth === undefined || depth < shallowestAliasDepth) {
	                            shallowestAliasDepth = depth;
	                        }
	                    }
	                }
	            }
	        }
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        candidates.sort((a, b) => {
	            if (a.depth !== b.depth) {
	                return a.depth - b.depth;
	            }
	            return sigma_engine_shared_1.Utils.naturalStringSort(a.name, b.name);
	        });
	        const shallowestOffPageDepth = candidates[0].depth;
	        if (shallowestAliasDepth !== undefined && shallowestAliasDepth < shallowestOffPageDepth) {
	            return undefined;
	        }
	        return candidates[0].name;
	    }
	}
	OrcadOffPageConnectorNamingRule.OrCadOffPageConnectorNamingRule = OrCadOffPageConnectorNamingRule;
	
	return OrcadOffPageConnectorNamingRule;
}

var OrcadHierarchicalPortNamingRule = {};

var hasRequiredOrcadHierarchicalPortNamingRule;

function requireOrcadHierarchicalPortNamingRule () {
	if (hasRequiredOrcadHierarchicalPortNamingRule) return OrcadHierarchicalPortNamingRule;
	hasRequiredOrcadHierarchicalPortNamingRule = 1;
	Object.defineProperty(OrcadHierarchicalPortNamingRule, "__esModule", { value: true });
	OrcadHierarchicalPortNamingRule.OrCadHierarchicalPortNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	const orcadHighestLevelWins_1 = requireOrcadHighestLevelWins();
	class OrCadHierarchicalPortNamingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        if (!group.hasLabelType('hierarchical-port')) {
	            return undefined;
	        }
	        if (!group.hasLabelType('alias') && this.instancesWithPinConnections(group) > 1) {
	            return undefined;
	        }
	        const candidates = [];
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            for (const net of nets) {
	                const netName = net.name.valueOf();
	                if (netName && !netName.startsWith('Net_auto_')) {
	                    candidates.push({ name: netName, depth });
	                }
	                for (const label of net.labels) {
	                    if (label.labelType === 'alias' || label.labelType === 'hierarchical-port') {
	                        candidates.push({
	                            name: label.formattedLabel ?? label.originalLabel,
	                            depth,
	                        });
	                    }
	                }
	            }
	        }
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        candidates.sort((a, b) => {
	            if (a.depth !== b.depth) {
	                return a.depth - b.depth;
	            }
	            return sigma_engine_shared_1.Utils.naturalStringSort(a.name, b.name);
	        });
	        if ((0, orcadHighestLevelWins_1.deferToShallowerSystemId)(group, candidates[0].depth)) {
	            return undefined;
	        }
	        return candidates[0].name;
	    }
	    instancesWithPinConnections(group) {
	        let count = 0;
	        for (const [, nets] of group.getNets()) {
	            let hasConnection = false;
	            for (const net of nets) {
	                if (net.connections.length > 0) {
	                    hasConnection = true;
	                    break;
	                }
	            }
	            if (hasConnection) {
	                count++;
	                if (count > 1) {
	                    return count;
	                }
	            }
	        }
	        return count;
	    }
	}
	OrcadHierarchicalPortNamingRule.OrCadHierarchicalPortNamingRule = OrCadHierarchicalPortNamingRule;
	
	return OrcadHierarchicalPortNamingRule;
}

var OrcadAliasNamingRule = {};

var hasRequiredOrcadAliasNamingRule;

function requireOrcadAliasNamingRule () {
	if (hasRequiredOrcadAliasNamingRule) return OrcadAliasNamingRule;
	hasRequiredOrcadAliasNamingRule = 1;
	Object.defineProperty(OrcadAliasNamingRule, "__esModule", { value: true });
	OrcadAliasNamingRule.OrCadAliasNamingRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const orcadHighestLevelWins_1 = requireOrcadHighestLevelWins();
	class OrCadAliasNamingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        if (!group.hasLabelType('alias')) {
	            return undefined;
	        }
	        let shallowestAliasDepth;
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            if (shallowestAliasDepth !== undefined && depth >= shallowestAliasDepth) {
	                continue;
	            }
	            if (nets.some((net) => net.labels.some((l) => l.labelType === 'alias'))) {
	                shallowestAliasDepth = depth;
	            }
	        }
	        if ((0, orcadHighestLevelWins_1.deferToShallowerSystemId)(group, shallowestAliasDepth)) {
	            return undefined;
	        }
	        return group
	            .getNetLabels()
	            .filter((l) => l.labelType === 'alias')
	            .map((l) => l.formattedLabel ?? l.originalLabel)
	            .sort()[0];
	    }
	}
	OrcadAliasNamingRule.OrCadAliasNamingRule = OrCadAliasNamingRule;
	
	return OrcadAliasNamingRule;
}

var OrcadSystemNameNamingRule = {};

var hasRequiredOrcadSystemNameNamingRule;

function requireOrcadSystemNameNamingRule () {
	if (hasRequiredOrcadSystemNameNamingRule) return OrcadSystemNameNamingRule;
	hasRequiredOrcadSystemNameNamingRule = 1;
	Object.defineProperty(OrcadSystemNameNamingRule, "__esModule", { value: true });
	OrcadSystemNameNamingRule.OrCadSystemNameNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	class OrCadSystemNameNamingRule {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	        this.assignedGroups = new Map();
	    }
	    apply(group, _hierarchy) {
	        let bestId;
	        let bestSuffix;
	        let bestDepth = Infinity;
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            for (const net of nets) {
	                for (const wire of net.wires) {
	                    const id = wire.strategyMetadata?.['system-id'];
	                    if (!id) {
	                        continue;
	                    }
	                    if (depth < bestDepth || (depth === bestDepth && (!bestId || sigma_engine_shared_1.Utils.naturalStringSort(id, bestId) < 0))) {
	                        bestId = id;
	                        bestDepth = depth;
	                        bestSuffix = instance.sheetSymbol?.Name;
	                    }
	                }
	            }
	        }
	        if (!bestId) {
	            return undefined;
	        }
	        const fullName = bestSuffix ? `${bestId}_${bestSuffix}` : bestId;
	        const entries = this.assignedGroups.get(bestId) ?? [];
	        entries.push({ group, fullName });
	        this.assignedGroups.set(bestId, entries);
	        return fullName;
	    }
	    simplifyNonConflicting() {
	        for (const [baseId, entries] of this.assignedGroups) {
	            if (entries.length === 1 && entries[0].fullName !== baseId) {
	                entries[0].group.physicalName = baseId;
	            }
	        }
	    }
	}
	OrcadSystemNameNamingRule.OrCadSystemNameNamingRule = OrCadSystemNameNamingRule;
	
	return OrcadSystemNameNamingRule;
}

var OrCadSystemNameSimplifyRule = {};

var hasRequiredOrCadSystemNameSimplifyRule;

function requireOrCadSystemNameSimplifyRule () {
	if (hasRequiredOrCadSystemNameSimplifyRule) return OrCadSystemNameSimplifyRule;
	hasRequiredOrCadSystemNameSimplifyRule = 1;
	Object.defineProperty(OrCadSystemNameSimplifyRule, "__esModule", { value: true });
	OrCadSystemNameSimplifyRule.OrCadSystemNameSimplifyRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrCadSystemNameSimplifyRule$1 {
	    constructor(systemNameRule) {
	        this.systemNameRule = systemNameRule;
	        this.type = NetNamingRules_1.NetNamingRuleType.Finalize;
	    }
	    apply(_groups, _hierarchy) {
	        this.systemNameRule.simplifyNonConflicting();
	    }
	}
	OrCadSystemNameSimplifyRule.OrCadSystemNameSimplifyRule = OrCadSystemNameSimplifyRule$1;
	
	return OrCadSystemNameSimplifyRule;
}

var OrcadPowerPinNamingRule = {};

var hasRequiredOrcadPowerPinNamingRule;

function requireOrcadPowerPinNamingRule () {
	if (hasRequiredOrcadPowerPinNamingRule) return OrcadPowerPinNamingRule;
	hasRequiredOrcadPowerPinNamingRule = 1;
	Object.defineProperty(OrcadPowerPinNamingRule, "__esModule", { value: true });
	OrcadPowerPinNamingRule.OrcadPowerPinNamingRule = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetNamingRules_1 = requireNetNamingRules();
	class OrcadPowerPinNamingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        const candidates = [];
	        for (const [instance, nets] of group.getNets()) {
	            const depth = instance.path.length;
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    const applies = conn.connection.appliesNetName;
	                    if (applies?.labelType === 'power-pin') {
	                        candidates.push({ name: applies.label.toUpperCase(), depth });
	                    }
	                }
	            }
	        }
	        if (candidates.length === 0) {
	            return undefined;
	        }
	        candidates.sort((a, b) => {
	            if (a.depth !== b.depth) {
	                return a.depth - b.depth;
	            }
	            return sigma_engine_shared_1.Utils.naturalStringSort(a.name, b.name);
	        });
	        return candidates[0].name;
	    }
	}
	OrcadPowerPinNamingRule.OrcadPowerPinNamingRule = OrcadPowerPinNamingRule$1;
	
	return OrcadPowerPinNamingRule;
}

var OrcadWirelessNetSystemNameRule = {};

var orcadModuleRef = {};

var hasRequiredOrcadModuleRef;

function requireOrcadModuleRef () {
	if (hasRequiredOrcadModuleRef) return orcadModuleRef;
	hasRequiredOrcadModuleRef = 1;
	Object.defineProperty(orcadModuleRef, "__esModule", { value: true });
	orcadModuleRef.placedPartDbIdFromModuleRef = placedPartDbIdFromModuleRef;
	function placedPartDbIdFromModuleRef(moduleRef) {
	    if (moduleRef.includes('::schref::')) {
	        return undefined;
	    }
	    const match = /::(\d+)$/.exec(moduleRef);
	    if (!match) {
	        return undefined;
	    }
	    const parsed = Number.parseInt(match[1], 10);
	    return Number.isNaN(parsed) ? undefined : parsed;
	}
	
	return orcadModuleRef;
}

var hasRequiredOrcadWirelessNetSystemNameRule;

function requireOrcadWirelessNetSystemNameRule () {
	if (hasRequiredOrcadWirelessNetSystemNameRule) return OrcadWirelessNetSystemNameRule;
	hasRequiredOrcadWirelessNetSystemNameRule = 1;
	Object.defineProperty(OrcadWirelessNetSystemNameRule, "__esModule", { value: true });
	OrcadWirelessNetSystemNameRule.OrcadWirelessNetSystemNameRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const orcadModuleRef_1 = requireOrcadModuleRef();
	class OrcadWirelessNetSystemNameRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        let owner;
	        let legacyDanglingOwner;
	        let connectionCount = 0;
	        for (const [, nets] of group.getNets()) {
	            for (const net of nets) {
	                if (net.wires.length > 0) {
	                    return undefined;
	                }
	                for (const connWithPos of net.connections) {
	                    connectionCount++;
	                    const dbId = (0, orcadModuleRef_1.placedPartDbIdFromModuleRef)(connWithPos.connection.moduleRef);
	                    if (dbId === undefined) {
	                        continue;
	                    }
	                    const pinNumber = connWithPos.connection.pinNumber ?? connWithPos.connection.pinRef;
	                    if (legacyDanglingOwner === undefined || dbId < legacyDanglingOwner.dbId) {
	                        legacyDanglingOwner = { dbId, pinNumber };
	                    }
	                    const pinPosition = connWithPos.connection.pinPosition;
	                    if (pinPosition === undefined) {
	                        continue;
	                    }
	                    if (owner === undefined || dbId < owner.dbId || (dbId === owner.dbId && pinPosition < owner.pinPosition)) {
	                        owner = { dbId, pinPosition };
	                    }
	                }
	            }
	        }
	        if (connectionCount === 1 && legacyDanglingOwner) {
	            return `N${String(legacyDanglingOwner.dbId).padStart(5, '0')}${legacyDanglingOwner.pinNumber}`;
	        }
	        if (!owner) {
	            return undefined;
	        }
	        return `N${String(owner.dbId).padStart(5, '0')}${owner.pinPosition}`;
	    }
	}
	OrcadWirelessNetSystemNameRule.OrcadWirelessNetSystemNameRule = OrcadWirelessNetSystemNameRule$1;
	
	return OrcadWirelessNetSystemNameRule;
}

var OrcadBusPortPositionalMergeRule = {};

var hasRequiredOrcadBusPortPositionalMergeRule;

function requireOrcadBusPortPositionalMergeRule () {
	if (hasRequiredOrcadBusPortPositionalMergeRule) return OrcadBusPortPositionalMergeRule;
	hasRequiredOrcadBusPortPositionalMergeRule = 1;
	Object.defineProperty(OrcadBusPortPositionalMergeRule, "__esModule", { value: true });
	OrcadBusPortPositionalMergeRule.OrcadBusPortPositionalMergeRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const OrcadDsnBusUtils_1 = requireOrcadDsnBusUtils();
	class OrcadBusPortPositionalMergeRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        for (const busGroup of manager.findGroupsWithObjectsOnDimension('bus')) {
	            this.processBusGroup(manager, busGroup);
	        }
	        return manager;
	    }
	    processBusGroup(manager, busGroup) {
	        for (const [instance, nets] of busGroup.getNets()) {
	            const portBusEntries = this.collectPortBusEntries(nets);
	            if (portBusEntries.length === 0) {
	                continue;
	            }
	            const busWires = this.collectBusWires(nets);
	            if (busWires.length === 0) {
	                continue;
	            }
	            for (const portBus of portBusEntries) {
	                const resolvedBus = this.findBusMembersAtPosition(busWires, portBus.position);
	                if (!resolvedBus) {
	                    continue;
	                }
	                this.mergePortToBus(manager, instance, portBus, resolvedBus);
	            }
	        }
	    }
	    collectPortBusEntries(nets) {
	        const results = [];
	        for (const net of nets) {
	            for (const obj of net.netObjects) {
	                const label = obj.label;
	                const meta = label?.strategyMetadata;
	                const portBus = meta?.['port-bus'];
	                const sheetSymbolDbId = meta?.['hierarchical-sheet-entry']?.sheetSymbolDbId;
	                if (portBus && sheetSymbolDbId && label) {
	                    results.push({
	                        members: portBus.members,
	                        sheetSymbolDbId,
	                        position: label.position,
	                    });
	                }
	            }
	        }
	        return results;
	    }
	    collectBusWires(nets) {
	        const results = [];
	        for (const net of nets) {
	            for (const obj of net.netObjects) {
	                const wire = obj.wire;
	                const bus = wire?.strategyMetadata?.bus;
	                if (wire && bus?.members && bus.members.length > 0) {
	                    results.push({ members: bus.members, name: bus.name, from: wire.from, to: wire.to });
	                }
	            }
	        }
	        return results;
	    }
	    findBusMembersAtPosition(busWires, position) {
	        for (const wire of busWires) {
	            const touchesStart = wire.from[0] === position[0] && wire.from[1] === position[1];
	            const touchesEnd = wire.to[0] === position[0] && wire.to[1] === position[1];
	            if (!touchesStart && !touchesEnd) {
	                continue;
	            }
	            const details = (0, OrcadDsnBusUtils_1.getBusDetails)(wire.name);
	            const byIndex = new Map(wire.members.map((m) => [m.index, m]));
	            if (details) {
	                const ordered = [];
	                for (let i = 0; i < details.count; i++) {
	                    const member = byIndex.get(details.start + i * details.step);
	                    if (member) {
	                        ordered.push(member);
	                    }
	                }
	                if (ordered.length === wire.members.length) {
	                    return { members: ordered, hasDeclaredRange: true };
	                }
	            }
	            return {
	                members: Array.from(wire.members).sort((a, b) => a.index - b.index),
	                hasDeclaredRange: details !== undefined,
	            };
	        }
	        return undefined;
	    }
	    mergePortToBus(manager, instance, portBus, resolvedBus) {
	        const busMembers = resolvedBus.members;
	        const portLiteralSet = new Set(portBus.members.map((member) => member.name.match(/(\d+)$/)?.[1]));
	        const busLiteralSet = new Set(busMembers.map((member) => member.name.match(/(\d+)$/)?.[1]));
	        const matchingLiteralSets = !portLiteralSet.has(undefined) &&
	            !busLiteralSet.has(undefined) &&
	            portLiteralSet.size === busLiteralSet.size &&
	            Array.from(portLiteralSet).every((literal) => busLiteralSet.has(literal));
	        const pairCount = Math.min(portBus.members.length, busMembers.length);
	        const pairs = [];
	        for (let i = 0; i < pairCount; i++) {
	            const portMember = portBus.members[i];
	            const exactName = busMembers.find((candidate) => candidate.name.toUpperCase() === portMember.name.toUpperCase());
	            const literalSuffix = portMember.name.match(/(\d+)$/)?.[1];
	            const suffixMatches = literalSuffix === undefined ?
	                []
	                : busMembers.filter((candidate) => candidate.name.match(/(\d+)$/)?.[1] === literalSuffix);
	            const busMember = exactName ??
	                (!resolvedBus.hasDeclaredRange && matchingLiteralSets && suffixMatches.length === 1 ?
	                    suffixMatches[0]
	                    : busMembers[i]);
	            const sheetEntryGroup = this.findSheetEntryGroup(manager, portMember.name, portBus.sheetSymbolDbId, instance);
	            if (!sheetEntryGroup) {
	                continue;
	            }
	            const wireGroup = this.findVirtualBusMemberGroup(manager, busMember.name, instance);
	            if (!wireGroup || wireGroup === sheetEntryGroup) {
	                continue;
	            }
	            pairs.push([sheetEntryGroup, wireGroup]);
	        }
	        for (const [sheetEntryGroup, wireGroup] of pairs) {
	            if (manager.resolveGroup(sheetEntryGroup) !== sheetEntryGroup || manager.resolveGroup(wireGroup) !== wireGroup) {
	                continue;
	            }
	            if (sheetEntryGroup === wireGroup) {
	                continue;
	            }
	            manager.mergeGroups(sheetEntryGroup, wireGroup);
	        }
	    }
	    findSheetEntryGroup(manager, memberName, sheetSymbolDbId, instance) {
	        const candidates = manager.findGroupsWithLabel('hierarchical-sheet-entry', memberName, instance);
	        for (const group of candidates) {
	            const match = group
	                .getNetLabels(instance)
	                .some((label) => label.labelType === 'hierarchical-sheet-entry' &&
	                (label.formattedLabel ?? label.originalLabel) === memberName &&
	                label.strategyMetadata?.['hierarchical-sheet-entry']?.sheetSymbolDbId === sheetSymbolDbId);
	            if (match) {
	                return group;
	            }
	        }
	        return undefined;
	    }
	    findVirtualBusMemberGroup(manager, memberName, instance) {
	        const candidates = manager.findGroupsWithLabel('virtual-bus-member', memberName, instance);
	        return candidates[0];
	    }
	}
	OrcadBusPortPositionalMergeRule.OrcadBusPortPositionalMergeRule = OrcadBusPortPositionalMergeRule$1;
	
	return OrcadBusPortPositionalMergeRule;
}

var OrcadInstanceScopeSuffixRule = {};

var hasRequiredOrcadInstanceScopeSuffixRule;

function requireOrcadInstanceScopeSuffixRule () {
	if (hasRequiredOrcadInstanceScopeSuffixRule) return OrcadInstanceScopeSuffixRule;
	hasRequiredOrcadInstanceScopeSuffixRule = 1;
	Object.defineProperty(OrcadInstanceScopeSuffixRule, "__esModule", { value: true });
	OrcadInstanceScopeSuffixRule.OrcadInstanceScopeSuffixRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	const LogicalSchema_1 = requireLogicalSchema();
	const OrcadDsnPortOccurrenceUtils_1 = requireOrcadDsnPortOccurrenceUtils();
	class OrcadInstanceScopeSuffixRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Finalize;
	        this.suffixableGroupBaseNames = new Map();
	        this.reservedDesignWideNames = new Set();
	    }
	    apply(groups, hierarchy) {
	        this.recordNewGroupsFromHierarchy(groups, hierarchy);
	        this.resetTrackedGroupsToBaseNames();
	        this.applyConflictSuffixes();
	    }
	    recordNewGroupsFromHierarchy(groups, hierarchy) {
	        const eligible = new Set();
	        for (const group of groups) {
	            if (!group.physicalName) {
	                continue;
	            }
	            if (this.isDesignWideUnifyingNamed(group)) {
	                this.reservedDesignWideNames.add(group.physicalName.toUpperCase());
	                continue;
	            }
	            eligible.add(group);
	        }
	        const visit = (node) => {
	            for (const group of eligible) {
	                if (!this.suffixableGroupBaseNames.has(group) && group.getNets().has(node.instance)) {
	                    this.suffixableGroupBaseNames.set(group, group.physicalName ?? '');
	                }
	            }
	            for (const child of node.children ?? []) {
	                visit(child);
	            }
	        };
	        visit(hierarchy);
	    }
	    resetTrackedGroupsToBaseNames() {
	        for (const [group, baseName] of this.suffixableGroupBaseNames) {
	            group.physicalName = baseName;
	        }
	    }
	    applyConflictSuffixes() {
	        const buckets = new Map();
	        for (const [group, baseName] of this.suffixableGroupBaseNames) {
	            let bucket = buckets.get(baseName);
	            if (!bucket) {
	                bucket = { hierarchicalPort: [], other: [] };
	                buckets.set(baseName, bucket);
	            }
	            if (group.hasLabelType('hierarchical-port') && !this.isAliasNamedNotPortNamed(group)) {
	                bucket.hierarchicalPort.push(group);
	            }
	            else {
	                bucket.other.push(group);
	            }
	        }
	        for (const [baseName, { hierarchicalPort, other }] of buckets) {
	            const collidesWithReservedGlobal = this.reservedDesignWideNames.has(baseName.toUpperCase());
	            this.suffixCollidingGroups(hierarchicalPort, collidesWithReservedGlobal);
	            this.suffixCollidingGroups(other, false);
	        }
	    }
	    suffixCollidingGroups(collidingGroups, collidesWithReservedGlobal) {
	        if (collidingGroups.length < 2 && !collidesWithReservedGlobal) {
	            return;
	        }
	        const plan = collidingGroups.map((group) => {
	            const suffixed = this.plannedSuffixedName(group);
	            return {
	                group,
	                finalName: suffixed ?? group.physicalName ?? '',
	                wasSuffixed: suffixed !== undefined && suffixed !== group.physicalName,
	            };
	        });
	        const entriesByFinalName = new Map();
	        for (const entry of plan) {
	            const list = entriesByFinalName.get(entry.finalName) ?? [];
	            list.push(entry);
	            entriesByFinalName.set(entry.finalName, list);
	        }
	        for (const [finalName, entries] of entriesByFinalName) {
	            if (entries.length === 1) {
	                const [entry] = entries;
	                if (entry.wasSuffixed) {
	                    entry.group.physicalName = entry.finalName;
	                }
	                continue;
	            }
	            const recordIds = entries.map((entry) => this.occurrenceNetSystemId(entry.group));
	            if (recordIds.some((id) => id === undefined) || new Set(recordIds).size !== recordIds.length) {
	                continue;
	            }
	            if (!this.groupsConfinedToDisjointOccurrenceBranches(entries.map((entry) => entry.group))) {
	                continue;
	            }
	            const smallestRecordId = Math.min(...recordIds);
	            for (const [index, entry] of entries.entries()) {
	                const recordId = recordIds[index];
	                entry.group.physicalName = recordId === smallestRecordId ? finalName : `${finalName}_${recordId}`;
	            }
	        }
	    }
	    groupsConfinedToDisjointOccurrenceBranches(groups) {
	        const branches = groups.map((group) => this.confinedOccurrenceBranch(group));
	        for (const branch of branches) {
	            for (const other of branches) {
	                if (branch === other || !branch || !other) {
	                    continue;
	                }
	                if (other.branchIds.has(branch.deepestId)) {
	                    return false;
	                }
	            }
	        }
	        return true;
	    }
	    confinedOccurrenceBranch(group) {
	        const subdesignInstances = Array.from(group.getNets().keys()).filter((instance) => instance.sheetSymbol);
	        if (subdesignInstances.length === 0) {
	            return undefined;
	        }
	        let deepest = subdesignInstances[0];
	        for (const instance of subdesignInstances) {
	            if (instance.path.length > deepest.path.length) {
	                deepest = instance;
	            }
	        }
	        const branchIds = new Set([deepest.pathUniqueId]);
	        for (const ancestor of deepest.path) {
	            if (ancestor.sheetSymbol) {
	                branchIds.add(ancestor.pathUniqueId);
	            }
	        }
	        return { deepestId: deepest.pathUniqueId, branchIds };
	    }
	    occurrenceNetSystemId(group) {
	        const base = (this.suffixableGroupBaseNames.get(group) ?? group.physicalName ?? '').toUpperCase();
	        const scopeSuffix = this.confinedOccurrenceScopeSuffix(group)?.toUpperCase();
	        const rawName = scopeSuffix && base.endsWith(`_${scopeSuffix}`) ? base.slice(0, -(scopeSuffix.length + 1)) : base;
	        const prefix = LogicalSchema_1.OrCadDSN.OCCURRENCE_NET_SYSTEM_ID_PROP_PREFIX;
	        for (const instance of group.getNets().keys()) {
	            const props = instance.instOccurence?.UserProperty;
	            if (!props) {
	                continue;
	            }
	            const prop = props.find((entry) => entry.Name === `${prefix}${rawName}` || entry.Name === `${prefix}${base}`);
	            if (!prop) {
	                continue;
	            }
	            const id = Number.parseInt(prop.Value, 10);
	            if (!Number.isNaN(id)) {
	                return id;
	            }
	        }
	        return undefined;
	    }
	    plannedSuffixedName(group) {
	        if (this.isCrossBoundaryNamed(group)) {
	            return undefined;
	        }
	        const suffix = this.confinedOccurrenceScopeSuffix(group);
	        if (!suffix || !group.physicalName) {
	            return undefined;
	        }
	        if (group.physicalName.toUpperCase().endsWith(`_${suffix}`)) {
	            return group.physicalName;
	        }
	        return `${group.physicalName}_${suffix}`;
	    }
	    confinedOccurrenceScopeSuffix(group) {
	        if (this.isAliasNamedNotPortNamed(group)) {
	            const ownsRootScopedNet = Array.from(group.getNets().keys()).some((instance) => !instance.sheetSymbol);
	            if (ownsRootScopedNet) {
	                return undefined;
	            }
	        }
	        const subdesignInstances = Array.from(group.getNets().keys()).filter((instance) => instance.sheetSymbol);
	        if (subdesignInstances.length === 0) {
	            return undefined;
	        }
	        const distinctSheetSymbols = new Set(subdesignInstances.map((instance) => instance.sheetSymbol));
	        if (distinctSheetSymbols.size !== 1) {
	            return undefined;
	        }
	        let deepest = subdesignInstances[0];
	        for (const instance of subdesignInstances) {
	            if (instance.path.length > deepest.path.length) {
	                deepest = instance;
	            }
	        }
	        const branchIds = new Set([deepest.pathUniqueId, ...deepest.path.map((p) => p.pathUniqueId)]);
	        for (const instance of subdesignInstances) {
	            if (!branchIds.has(instance.pathUniqueId)) {
	                return undefined;
	            }
	        }
	        const chain = group.hasLabelType('hierarchical-port') ? [deepest] : [...deepest.path, deepest];
	        const parts = [];
	        for (const instance of chain) {
	            if (instance.sheetSymbol) {
	                parts.push(instance.sheetSymbol.Name.toUpperCase());
	            }
	        }
	        if (parts.length === 0) {
	            return undefined;
	        }
	        return parts.join('_');
	    }
	    isAliasNamedNotPortNamed(group) {
	        const base = group.physicalName;
	        if (!base) {
	            return false;
	        }
	        const upperBase = base.toUpperCase();
	        let hasMatchingAlias = false;
	        for (const label of group.getNetLabels()) {
	            if (label.originalLabel.toUpperCase() !== upperBase) {
	                continue;
	            }
	            if (label.labelType === 'hierarchical-port' || label.labelType === 'virtual-bus-member') {
	                return false;
	            }
	            if (label.labelType === 'alias') {
	                hasMatchingAlias = true;
	            }
	        }
	        return hasMatchingAlias;
	    }
	    isCrossBoundaryNamed(group) {
	        return this.isDesignWideUnifyingNamed(group) || this.crossesOccurrenceBoundary(group);
	    }
	    crossesOccurrenceBoundary(group) {
	        const parentKeys = new Set();
	        const childKeys = new Set();
	        for (const label of group.getNetLabels()) {
	            const occurrence = label.strategyMetadata?.['port-occurrence'];
	            if (!occurrence) {
	                continue;
	            }
	            if (label.labelType === 'hierarchical-sheet-entry') {
	                parentKeys.add((0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence));
	            }
	            else if (label.labelType === 'hierarchical-port') {
	                childKeys.add((0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence));
	            }
	        }
	        return Array.from(parentKeys).some((key) => childKeys.has(key));
	    }
	    isDesignWideUnifyingNamed(group) {
	        if (group.hasLabelType('global')) {
	            return true;
	        }
	        for (const [, nets] of group.getNets()) {
	            for (const net of nets) {
	                for (const conn of net.connections) {
	                    if (conn.connection.appliesNetName?.labelType === 'global') {
	                        return true;
	                    }
	                }
	            }
	        }
	        return false;
	    }
	}
	OrcadInstanceScopeSuffixRule.OrcadInstanceScopeSuffixRule = OrcadInstanceScopeSuffixRule$1;
	
	return OrcadInstanceScopeSuffixRule;
}

var OrcadAliasCollisionPhysicalNameRule = {};

var hasRequiredOrcadAliasCollisionPhysicalNameRule;

function requireOrcadAliasCollisionPhysicalNameRule () {
	if (hasRequiredOrcadAliasCollisionPhysicalNameRule) return OrcadAliasCollisionPhysicalNameRule;
	hasRequiredOrcadAliasCollisionPhysicalNameRule = 1;
	Object.defineProperty(OrcadAliasCollisionPhysicalNameRule, "__esModule", { value: true });
	OrcadAliasCollisionPhysicalNameRule.OrcadAliasCollisionPhysicalNameRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrcadAliasCollisionPhysicalNameRule$1 {
	    constructor(reportDiagnostic) {
	        this.reportDiagnostic = reportDiagnostic;
	        this.type = NetNamingRules_1.NetNamingRuleType.Finalize;
	    }
	    apply(groups) {
	        const collisions = new Map();
	        const blocked = new Map();
	        for (const group of groups) {
	            const instances = [...group.getNets().keys()];
	            const schematics = new Set(instances.map((instance) => instance.hierarchyEntry.schematic));
	            const [schematic] = schematics;
	            const labels = group.getNetLabels();
	            const aliases = new Set(labels
	                .filter((label) => label.labelType === 'alias' || label.labelType === 'off-page-connector')
	                .map((label) => label.originalLabel.toUpperCase()));
	            const [alias] = aliases;
	            const reason = schematics.size !== 1 ? 'multiple-schematics'
	                : schematic?.StoredPhysicalNetNames === undefined ? 'stored-names-absent'
	                    : instances.some((instance) => instance.sheetSymbol) ? 'hierarchical-instance'
	                        : (labels.some((label) => ['global', 'hierarchical-port', 'hierarchical-sheet-entry', 'virtual-bus-member'].includes(label.labelType))) ?
	                            'higher-priority-carrier'
	                            : aliases.size !== 1 || !alias ? 'alias-not-unique'
	                                : undefined;
	            if (reason !== undefined || !schematic || !alias) {
	                this.reportSkip(group, reason ?? 'owner-absent');
	                for (const owner of schematics) {
	                    const names = blocked.get(owner) ?? new Set();
	                    for (const name of aliases) {
	                        names.add(name);
	                    }
	                    blocked.set(owner, names);
	                }
	                continue;
	            }
	            const byAlias = collisions.get(schematic) ?? new Map();
	            const members = byAlias.get(alias) ?? [];
	            members.push(this.member(group));
	            byAlias.set(alias, members);
	            collisions.set(schematic, byAlias);
	        }
	        for (const [schematic, byAlias] of collisions) {
	            for (const [alias, members] of byAlias) {
	                if (members.length < 2 || blocked.get(schematic)?.has(alias)) {
	                    for (const member of members) {
	                        this.reportSkip(member.group, blocked.get(schematic)?.has(alias) ? 'collision-member-ineligible' : 'no-collision');
	                    }
	                    continue;
	                }
	                const keys = members.map((member) => member.key);
	                const decodedKeys = keys.filter((key) => key !== undefined);
	                if (decodedKeys.length !== members.length || new Set(decodedKeys).size !== members.length) {
	                    this.reportDiagnostic(`CFB physical net name [physical-name-rule-applied] approval=pending alias=${JSON.stringify(alias)} keys=${JSON.stringify(keys)} [physical-name-key-ambiguous]`);
	                    for (const member of members) {
	                        this.reportSkip(member.group, 'physical-name-key-ambiguous');
	                    }
	                    continue;
	                }
	                const stored = schematic.StoredPhysicalNetNames ?? [];
	                const suffixed = stored.filter((name) => name.startsWith(`${alias}_`));
	                if (suffixed.length !== members.length - 1 || new Set(suffixed).size !== suffixed.length) {
	                    this.reportDiagnostic(`CFB physical net name [physical-name-count-mismatch] alias=${JSON.stringify(alias)} nets=${members.length - 1} stored=${JSON.stringify(suffixed)}`);
	                    for (const member of members) {
	                        this.reportSkip(member.group, 'physical-name-count-mismatch');
	                    }
	                    continue;
	                }
	                const candidates = members.map((member) => `${alias}_${member.key}`);
	                const absent = candidates.filter((candidate) => !stored.includes(candidate));
	                if (absent.length !== 1 ||
	                    stored.filter((name) => name === alias).length !== 1 ||
	                    suffixed.some((name) => !candidates.includes(name))) {
	                    for (const member of members) {
	                        this.reportSkip(member.group, 'physical-name-uncorroborated');
	                    }
	                    this.reportDiagnostic(`CFB physical net name [physical-name-uncorroborated] computed=${JSON.stringify(candidates)} stored=${JSON.stringify(stored)} absent=${JSON.stringify(absent)}`);
	                    continue;
	                }
	                for (const member of members) {
	                    const candidate = `${alias}_${member.key}`;
	                    const computed = stored.includes(candidate) ? candidate : alias;
	                    this.reportDiagnostic(`CFB physical net name [physical-name-rule-applied] approval=pending alias=${JSON.stringify(alias)} key=${member.key} computed=${JSON.stringify(computed)}`);
	                    if (member.group.physicalName &&
	                        member.group.physicalName !== alias &&
	                        member.group.physicalName !== computed) {
	                        this.reportSkip(member.group, 'existing-name-conflict');
	                    }
	                    else {
	                        member.group.physicalName = computed;
	                    }
	                }
	            }
	        }
	    }
	    reportSkip(group, reason) {
	        const members = [...group.getNets()].map(([instance, nets]) => ({
	            path: instance.pathUniqueId,
	            page: instance.hierarchyEntry.page.Name,
	            wires: nets.flatMap((net) => net.wires.map((wire) => wire.strategyMetadata?.['source-wire-db-id'])),
	        }));
	        this.reportDiagnostic(`CFB physical net name [physical-name-rule-skipped] reason=${reason} group=${JSON.stringify(members)}`);
	    }
	    member(group) {
	        const ids = [];
	        let incomplete = false;
	        for (const [instance, nets] of group.getNets()) {
	            const placements = new Map((instance.hierarchyEntry.page.PartInst ?? []).map((part) => [
	                `${instance.pathUniqueId}::${part.DbId}`,
	                part.DbId,
	            ]));
	            for (const net of nets) {
	                for (const wire of net.wires) {
	                    const id = wire.strategyMetadata?.['source-wire-db-id'];
	                    if (id === undefined || !Number.isSafeInteger(id) || id < 0) {
	                        incomplete = true;
	                    }
	                    else {
	                        ids.push(id);
	                    }
	                }
	                for (const connection of net.connections) {
	                    const id = placements.get(connection.connection.moduleRef);
	                    if (id === undefined || !Number.isSafeInteger(id) || id < 0) {
	                        incomplete = true;
	                    }
	                    else {
	                        ids.push(id);
	                    }
	                }
	            }
	        }
	        return { group, key: incomplete || ids.length === 0 ? undefined : Math.min(...ids) };
	    }
	}
	OrcadAliasCollisionPhysicalNameRule.OrcadAliasCollisionPhysicalNameRule = OrcadAliasCollisionPhysicalNameRule$1;
	
	return OrcadAliasCollisionPhysicalNameRule;
}

var OrcadPowerPortLocalGlobalMergeRule = {};

var hasRequiredOrcadPowerPortLocalGlobalMergeRule;

function requireOrcadPowerPortLocalGlobalMergeRule () {
	if (hasRequiredOrcadPowerPortLocalGlobalMergeRule) return OrcadPowerPortLocalGlobalMergeRule;
	hasRequiredOrcadPowerPortLocalGlobalMergeRule = 1;
	Object.defineProperty(OrcadPowerPortLocalGlobalMergeRule, "__esModule", { value: true });
	OrcadPowerPortLocalGlobalMergeRule.OrcadPowerPortLocalGlobalMergeRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class OrcadPowerPortLocalGlobalMergeRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Group;
	    }
	    apply(manager, _hierarchy) {
	        const portGroups = new Map();
	        for (const group of manager.iterateGroups()) {
	            for (const [instance, nets] of group.getNets()) {
	                const scope = this.subdesignScope(instance);
	                for (const net of nets) {
	                    for (const label of net.labels) {
	                        if (label.labelType !== 'hierarchical-port') {
	                            continue;
	                        }
	                        const name = (label.formattedLabel ?? label.originalLabel).toUpperCase();
	                        const key = this.bucketKey(scope, name);
	                        const groups = portGroups.get(key) ?? new Set();
	                        groups.add(group);
	                        portGroups.set(key, groups);
	                    }
	                }
	            }
	        }
	        if (portGroups.size === 0) {
	            return manager;
	        }
	        const mergeBuckets = new Map();
	        for (const group of manager.iterateGroups()) {
	            for (const [instance, nets] of group.getNets()) {
	                const scope = this.subdesignScope(instance);
	                for (const net of nets) {
	                    for (const label of net.labels) {
	                        if (label.labelType !== 'global') {
	                            continue;
	                        }
	                        const name = (label.formattedLabel ?? label.originalLabel).toUpperCase();
	                        const key = this.bucketKey(scope, name);
	                        const matchingPortGroups = portGroups.get(key);
	                        if (!matchingPortGroups) {
	                            continue;
	                        }
	                        let bucket = mergeBuckets.get(key);
	                        if (!bucket) {
	                            bucket = new Set();
	                            for (const portGroup of matchingPortGroups) {
	                                bucket.add(portGroup);
	                            }
	                            mergeBuckets.set(key, bucket);
	                        }
	                        bucket.add(group);
	                    }
	                    for (const conn of net.connections) {
	                        const applies = conn.connection.appliesNetName;
	                        if (!applies || applies.labelType !== 'global') {
	                            continue;
	                        }
	                        const name = applies.label.toUpperCase();
	                        const key = this.bucketKey(scope, name);
	                        const matchingPortGroups = portGroups.get(key);
	                        if (!matchingPortGroups) {
	                            continue;
	                        }
	                        let bucket = mergeBuckets.get(key);
	                        if (!bucket) {
	                            bucket = new Set();
	                            for (const portGroup of matchingPortGroups) {
	                                bucket.add(portGroup);
	                            }
	                            mergeBuckets.set(key, bucket);
	                        }
	                        bucket.add(group);
	                    }
	                }
	            }
	        }
	        for (const groups of mergeBuckets.values()) {
	            if (groups.size <= 1) {
	                continue;
	            }
	            manager.mergeGroups(...Array.from(groups));
	        }
	        return manager;
	    }
	    subdesignScope(instance) {
	        return instance.sheetSymbol ? instance.pathUniqueId : '__top__';
	    }
	    bucketKey(scope, name) {
	        return `${scope}\0${name}`;
	    }
	}
	OrcadPowerPortLocalGlobalMergeRule.OrcadPowerPortLocalGlobalMergeRule = OrcadPowerPortLocalGlobalMergeRule$1;
	
	return OrcadPowerPortLocalGlobalMergeRule;
}

var hasRequiredOrcadNetNamingStrategy;

function requireOrcadNetNamingStrategy () {
	if (hasRequiredOrcadNetNamingStrategy) return OrcadNetNamingStrategy;
	hasRequiredOrcadNetNamingStrategy = 1;
	Object.defineProperty(OrcadNetNamingStrategy, "__esModule", { value: true });
	OrcadNetNamingStrategy.OrCadNetNamingStrategy = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetCombobulator_1 = requireNetCombobulator();
	const NetNamingRuleChain_1 = requireNetNamingRuleChain();
	const IndividualGroupingRule_1 = requireIndividualGroupingRule();
	const FallbackNetNamingRule_1 = requireFallbackNetNamingRule();
	const OrcadGlobalSignalGroupingRule_1 = requireOrcadGlobalSignalGroupingRule();
	const OrcadHierarchicalPortGroupingRule_1 = requireOrcadHierarchicalPortGroupingRule();
	const OrcadBusMemberGroupingRule_1 = requireOrcadBusMemberGroupingRule();
	const OrcadBusMemberNamingRule_1 = requireOrcadBusMemberNamingRule();
	const OrcadNetScalarGroupingRule_1 = requireOrcadNetScalarGroupingRule();
	const OrcadOffPageConnectorGroupingRule_1 = requireOrcadOffPageConnectorGroupingRule();
	const OrcadGlobalSignalNamingRule_1 = requireOrcadGlobalSignalNamingRule();
	const OrcadOffPageConnectorNamingRule_1 = requireOrcadOffPageConnectorNamingRule();
	const OrcadHierarchicalPortNamingRule_1 = requireOrcadHierarchicalPortNamingRule();
	const OrcadAliasNamingRule_1 = requireOrcadAliasNamingRule();
	const OrcadSystemNameNamingRule_1 = requireOrcadSystemNameNamingRule();
	const OrCadSystemNameSimplifyRule_1 = requireOrCadSystemNameSimplifyRule();
	const OrcadPowerPinNamingRule_1 = requireOrcadPowerPinNamingRule();
	const OrcadWirelessNetSystemNameRule_1 = requireOrcadWirelessNetSystemNameRule();
	const OrcadBusPortPositionalMergeRule_1 = requireOrcadBusPortPositionalMergeRule();
	const OrcadInstanceScopeSuffixRule_1 = requireOrcadInstanceScopeSuffixRule();
	const OrcadAliasCollisionPhysicalNameRule_1 = requireOrcadAliasCollisionPhysicalNameRule();
	const OrcadPowerPortLocalGlobalMergeRule_1 = requireOrcadPowerPortLocalGlobalMergeRule();
	class OrCadNetNamingStrategy {
	    static { this.AUTO_NET_PREFIX = 'Net_auto_'; }
	    constructor(enableDebug = false, reportHierarchyDiagnostic = () => undefined) {
	        this.systemNameRule = new OrcadSystemNameNamingRule_1.OrCadSystemNameNamingRule();
	        this.ruleChain = this.buildRuleChain(enableDebug, reportHierarchyDiagnostic);
	    }
	    get debugRuleChain() {
	        return this.ruleChain;
	    }
	    buildRuleChain(enableDebug = false, reportHierarchyDiagnostic = () => undefined) {
	        const chain = new NetNamingRuleChain_1.NetNamingRuleChain(enableDebug);
	        chain.add(new IndividualGroupingRule_1.IndividualGroupingRule());
	        chain.add(new OrcadNetScalarGroupingRule_1.OrCadNetScalarGroupingRule());
	        chain.add(new OrcadPowerPortLocalGlobalMergeRule_1.OrcadPowerPortLocalGlobalMergeRule());
	        chain.add(new OrcadBusPortPositionalMergeRule_1.OrcadBusPortPositionalMergeRule());
	        chain.add(new OrcadHierarchicalPortGroupingRule_1.OrCadHierarchicalPortGroupingRule(reportHierarchyDiagnostic));
	        chain.add(new OrcadBusMemberGroupingRule_1.OrcadBusMemberGroupingRule());
	        chain.add(new OrcadGlobalSignalGroupingRule_1.OrCadGlobalSignalGroupingRule());
	        chain.add(new OrcadOffPageConnectorGroupingRule_1.OrCadOffPageConnectorGroupingRule());
	        chain.add(new OrcadGlobalSignalNamingRule_1.OrCadGlobalSignalNamingRule());
	        chain.add(new OrcadOffPageConnectorNamingRule_1.OrCadOffPageConnectorNamingRule());
	        chain.add(new OrcadHierarchicalPortNamingRule_1.OrCadHierarchicalPortNamingRule());
	        chain.add(new OrcadBusMemberNamingRule_1.OrcadBusMemberNamingRule());
	        chain.add(new OrcadAliasNamingRule_1.OrCadAliasNamingRule());
	        chain.add(this.systemNameRule);
	        chain.add(new OrcadPowerPinNamingRule_1.OrcadPowerPinNamingRule());
	        chain.add(new OrcadWirelessNetSystemNameRule_1.OrcadWirelessNetSystemNameRule());
	        chain.add(new FallbackNetNamingRule_1.FallbackNetNamingRule('Net_UNKNOWN'));
	        chain.add(new OrCadSystemNameSimplifyRule_1.OrCadSystemNameSimplifyRule(this.systemNameRule));
	        chain.add(new OrcadInstanceScopeSuffixRule_1.OrcadInstanceScopeSuffixRule());
	        chain.add(new OrcadAliasCollisionPhysicalNameRule_1.OrcadAliasCollisionPhysicalNameRule(reportHierarchyDiagnostic));
	        return chain;
	    }
	    transformNetName(netObject, _instance) {
	        if (netObject.connection?.connection.appliesNetName &&
	            netObject.connection.connection.appliesNetName.labelType !== 'power-pin') {
	            return netObject.connection.connection.appliesNetName.label.toUpperCase();
	        }
	        if (netObject.label) {
	            return netObject.label.originalLabel.toUpperCase();
	        }
	        return undefined;
	    }
	    canLabelTypeNameNets(labelType, _instance) {
	        switch (labelType) {
	            case 'alias':
	            case 'global':
	            case 'off-page-connector':
	            case 'hierarchical-port':
	            case 'virtual-bus-member':
	            case 'power-pin':
	                return true;
	            case 'hierarchical-sheet-entry':
	                return false;
	            default:
	                return false;
	        }
	    }
	    resolveNetName(net, _instance) {
	        for (const connWithPos of net.connections) {
	            const applies = connWithPos.connection.appliesNetName;
	            if (applies && applies.labelType !== 'power-pin') {
	                return applies.label.toUpperCase();
	            }
	        }
	        for (const label of net.labels) {
	            if (label.originalLabel && label.canNameNet) {
	                return label.originalLabel.toUpperCase();
	            }
	        }
	        const base = net.name.valueOf();
	        return base.startsWith(OrCadNetNamingStrategy.AUTO_NET_PREFIX) ? base : (`${OrCadNetNamingStrategy.AUTO_NET_PREFIX}${base}`);
	    }
	    resolvePhysicalNetNames(hierarchy) {
	        return this.ruleChain.execute(hierarchy);
	    }
	    createVirtualRootHierarchy(roots) {
	        if (roots.length === 0) {
	            throw new Error('createVirtualRootHierarchy requires at least one root');
	        }
	        const firstChildInstance = roots[0].instance;
	        const virtualInstance = {
	            hierarchyEntry: firstChildInstance.hierarchyEntry,
	            path: [],
	            children: roots.map((r) => r.instance),
	            pathUniqueId: '__virtual_root__',
	            numInstances: 1,
	            totalSheetInstances: 1,
	            instanceIndex: 0,
	            sheetSymbol: undefined,
	            name: '__virtual_root__',
	            designator: '',
	            netRefCtx: new sigma_engine_shared_1.NetRefCtx(),
	            isMultiInstanceSheet: false,
	            instOccurence: firstChildInstance.instOccurence,
	        };
	        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(virtualInstance.netRefCtx, this, virtualInstance, {
	            autoAssignNets: false,
	            pointRoundingPrecision: 0.1,
	        });
	        return {
	            pathUniqueId: '__virtual_root__',
	            hierarchyDepth: -1,
	            instance: virtualInstance,
	            combobulator: emptyCombobulator,
	            children: roots,
	        };
	    }
	}
	OrcadNetNamingStrategy.OrCadNetNamingStrategy = OrCadNetNamingStrategy;
	
	return OrcadNetNamingStrategy;
}

var hasRequiredOrcadDsnTransformSchematicGenerator;

function requireOrcadDsnTransformSchematicGenerator () {
	if (hasRequiredOrcadDsnTransformSchematicGenerator) return OrcadDsnTransformSchematicGenerator;
	hasRequiredOrcadDsnTransformSchematicGenerator = 1;
	Object.defineProperty(OrcadDsnTransformSchematicGenerator, "__esModule", { value: true });
	OrcadDsnTransformSchematicGenerator.OrCadDsnTransformSchematicGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const fonts_1 = requireFonts();
	const TransformProject_1 = requireTransformProject();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformSchematicLayerSet_1 = requireTransformSchematicLayerSet();
	const OrcadNetNamingStrategy_1 = requireOrcadNetNamingStrategy();
	const NetCombobulator_1 = requireNetCombobulator();
	const TransformMultiInstanceSchematic_1 = requireTransformMultiInstanceSchematic();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const orcadLayers_1 = requireOrcadLayers();
	const orcadLogicalSchematicReferenceResolver_1 = requireOrcadLogicalSchematicReferenceResolver();
	class OrCadDsnTransformSchematicGenerator {
	    constructor() {
	        this.runsFor = [TransformProject_1.TransformProject];
	    }
	    static { this.COMBOBULATOR_EXPLICIT_WIRE_INTERSECTIONS = true; }
	    *generate(parent, rootContext) {
	        const context = parent.context;
	        const allInstances = yield* this.buildSchematicHierarchyInstances(parent, context, context.rootHierarchies);
	        yield* this.generateSchematicsForHierarchyInstances(parent, allInstances, context);
	        rootContext.pageSchematicRecords = [
	            ...(rootContext.pageSchematicRecords ?? []),
	            ...(context.pageSchematicRecords ?? []),
	        ];
	        const hierarchyNodes = this.buildDesignHierarchyNodes(allInstances, context);
	        rootContext.designHierarchyResults = rootContext.designHierarchyResults ?? [];
	        rootContext.designHierarchyResults.push(hierarchyNodes);
	    }
	    *generateSchematicsForHierarchyInstances(project, allInstances, context) {
	        context.hierarchyPortDiagnostics = context.hierarchyPortDiagnostics ?? new Set();
	        const namingStrategy = new OrcadNetNamingStrategy_1.OrCadNetNamingStrategy(context.options.debugNetNamingChain, (message) => context.hierarchyPortDiagnostics?.add(message));
	        for (const [hierarchy, instances] of allInstances) {
	            let multiInstanceSchematic;
	            if (instances.length > 1) {
	                const pageNumbers = new Set(instances.map((i) => this.getInstancePageNumber(i, 'Schematic Page Number')));
	                const pageNumber = pageNumbers.size === 1 ? Array.from(pageNumbers)[0] : 1;
	                if (pageNumbers.size > 1) {
	                    yield new TransformProcessingResult_1.TransformProcessingResult(project, {
	                        type: 'orcad-dsn-transformer',
	                        message: `Multiple instances of the same Design+Schematic+Page found, but with different page numbers. This is not supported. Will use the first page number: ${pageNumber}`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                        pageNumbers: Array.from(pageNumbers),
	                    });
	                }
	                const multiInstanceCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), namingStrategy, {
	                    ...instances[0],
	                    isMultiInstanceSheet: true,
	                }, {
	                    explicitWireIntersections: OrCadDsnTransformSchematicGenerator.COMBOBULATOR_EXPLICIT_WIRE_INTERSECTIONS,
	                });
	                const isExternalDsnMulti = hierarchy.design !== context.rootDesign;
	                const multiExternalSourceFile = context.designSourceFiles.get(hierarchy.design) ?? hierarchy.sourceFile;
	                const multiFilePrefix = isExternalDsnMulti ? `${multiExternalSourceFile}: ` : '';
	                multiInstanceSchematic = new TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic(project, {
	                    designId: (0, OrcadDsnUtils_1.orcadDesignId)(hierarchy.name),
	                    designName: `${multiFilePrefix}${hierarchy.schematic.Name} - ${hierarchy.page.Name}`,
	                    sheetIndex: pageNumber,
	                    sourceFile: hierarchy.sourceFile,
	                    instances,
	                    sheetDisplayPath: [],
	                }, multiInstanceCombobulator, context);
	                yield multiInstanceSchematic;
	            }
	            for (const instance of instances) {
	                const netCb = new NetCombobulator_1.NetCombobulator(instance.netRefCtx, namingStrategy, instance, {
	                    explicitWireIntersections: OrCadDsnTransformSchematicGenerator.COMBOBULATOR_EXPLICIT_WIRE_INTERSECTIONS,
	                });
	                const schematic = new TransformSchematic_1.TransformSchematic(multiInstanceSchematic ?? project, {
	                    designId: (0, OrcadDsnUtils_1.orcadDesignId)(`${instance.hierarchyEntry.slug}|${instance.pathUniqueId}`),
	                    designName: instance.name,
	                    sheetIndex: this.getInstancePageNumber(instance, 'Page Number'),
	                    sourceFile: instance.hierarchyEntry.sourceFile,
	                    font: { default: fonts_1.Font.Newstroke },
	                    instance,
	                }, netCb, context);
	                yield schematic;
	                yield this.createLayerSet(schematic);
	                context.instanceSchematicMap.set(instance, schematic);
	                context.pageSchematicRecords = context.pageSchematicRecords ?? [];
	                context.pageSchematicRecords.push({ page: hierarchy.page, schematic });
	            }
	        }
	    }
	    getInstancePageNumber(i, numberType) {
	        const foundNumbers = new Set((0, sigma_engine_shared_1.toArray)(i.instOccurence.TitleBlockOccurrence)
	            .map((t) => OrcadDsnUtils_1.OrcadDsnUtils.getTitleBlockUserProp(t, numberType)?.Value)
	            .filter((v) => v !== undefined)
	            .map((v) => parseInt(v, 10)));
	        if (foundNumbers.size === 0) {
	            return 1;
	        }
	        return Array.from(foundNumbers)[0] ?? 1;
	    }
	    *buildSchematicHierarchyInstances(project, context, rootHierarchies, hierarchyInstances = new Map(), path = [], pathUniqueId = [], sheetSymbol) {
	        for (const rootHierarchy of rootHierarchies) {
	            if (path.some((ancestor) => ancestor.hierarchyEntry === rootHierarchy)) {
	                const joinedPath = pathUniqueId.join('/');
	                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
	                    type: 'orcad-dsn-transformer',
	                    message: `Self-referential hierarchy skipped for ${rootHierarchy.design.Name} - ${rootHierarchy.schematic.Name} - ${rootHierarchy.page.Name}.`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    pathUniqueId: joinedPath.length > 0 ? joinedPath : '/',
	                });
	                continue;
	            }
	            const parentInstOccurences = path.length > 0 ?
	                path[path.length - 1].instOccurence.InstOccurrence
	                : context.rootDesign.Occurrences.InstOccurrence;
	            if (!parentInstOccurences) {
	                throw new Error(`No parent inst occurences found`);
	            }
	            const myInstOccurence = parentInstOccurences.find((o) => o.RefInstId === (sheetSymbol?.DbId.toString() ?? '-1'));
	            if (!myInstOccurence) {
	                throw new Error(`No inst occurence found for ${sheetSymbol?.DbId.toString() ?? '-1'} in ${parentInstOccurences.map((o) => o.RefInstId).join(', ')}`);
	            }
	            const designator = sheetSymbol ?
	                (myInstOccurence.UserProperty?.find((p) => p.Name === 'Reference')?.Value ?? sheetSymbol.DeviceDesignator)
	                : '';
	            const myPathUniqueIdParts = [...pathUniqueId, `${sheetSymbol?.DbId.toString() ?? ''}_${designator}`];
	            const myPathUniqueId = myPathUniqueIdParts.join('/') || '/';
	            const schematicPath = OrcadDsnUtils_1.OrcadDsnUtils.getTitleBlockUserProp(myInstOccurence.TitleBlockOccurrence, 'Schematic Path')?.Value;
	            if (!schematicPath) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(project, {
	                    type: 'orcad-dsn-transformer',
	                    message: `No schematic path found for ${rootHierarchy.design.Name} - ${rootHierarchy.schematic.Name} - ${rootHierarchy.page.Name}. Could not validate pathUniqueId.`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    pathUniqueId: myPathUniqueId,
	                });
	            }
	            const isExternalDsn = rootHierarchy.design !== context.rootDesign;
	            const externalSourceFile = context.designSourceFiles.get(rootHierarchy.design) ?? rootHierarchy.sourceFile;
	            const filePrefix = isExternalDsn ? `${externalSourceFile}: ` : '';
	            const baseName = `${filePrefix}${rootHierarchy.schematic.Name} - ${rootHierarchy.page.Name}`;
	            const isRoot = path.length === 0;
	            let displayName;
	            if (isRoot) {
	                displayName = baseName;
	            }
	            else {
	                const pathSegments = [
	                    ...path.map((p) => ({ designator: p.designator, sheetSymbol: p.sheetSymbol })),
	                    { designator, sheetSymbol },
	                ];
	                const readablePath = pathSegments
	                    .map((seg) => {
	                    if (!seg.designator) {
	                        return '';
	                    }
	                    return seg.designator.includes('?') && seg.sheetSymbol ?
	                        `${seg.designator}#${seg.sheetSymbol.DbId}`
	                        : seg.designator;
	                })
	                    .filter(Boolean)
	                    .join('/');
	                displayName = readablePath ? `${baseName} (/${readablePath})` : baseName;
	            }
	            const instance = {
	                name: displayName,
	                designator,
	                hierarchyEntry: rootHierarchy,
	                path: Array.from(path),
	                pathUniqueId: myPathUniqueId,
	                numInstances: 1,
	                totalSheetInstances: 0,
	                instanceIndex: hierarchyInstances.get(rootHierarchy)?.length ?? 0,
	                sheetSymbol,
	                isMultiInstanceSheet: false,
	                netRefCtx: new sigma_engine_shared_1.NetRefCtx(),
	                instOccurence: myInstOccurence,
	                children: [],
	            };
	            const arr = hierarchyInstances.get(rootHierarchy) ?? [];
	            arr.push(instance);
	            hierarchyInstances.set(rootHierarchy, arr);
	            const children = context.pageChildren.get(rootHierarchy) ?? [];
	            for (const child of children) {
	                const candidates = Array.from(new Set(context.allHierarchies.map((hierarchy) => hierarchy.sourceFile)), (sourceFile) => ({
	                    sourceFile,
	                    schematicViewNames: context.allHierarchies
	                        .filter((hierarchy) => hierarchy.sourceFile === sourceFile)
	                        .map((hierarchy) => hierarchy.schematic.Name),
	                }));
	                const parentStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(rootHierarchy.sourceFile).replace(/\.(dsn|xml)$/i, '');
	                const childStem = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(child.targetFile).replace(/\.(dsn|xml)$/i, '');
	                const resolution = parentStem === childStem ? undefined : ((0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(candidates, rootHierarchy.sourceFile, child.targetFile, child.targetSchematic));
	                const targetSourceFile = parentStem === childStem ? (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(rootHierarchy.sourceFile)
	                    : resolution?.status === 'resolved' ? (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(resolution.candidate.sourceFile)
	                        : undefined;
	                const childHierarchies = context.allHierarchies.filter((hierarchy) => targetSourceFile !== undefined &&
	                    (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(hierarchy.sourceFile) === targetSourceFile &&
	                    hierarchy.schematic.Name.toLowerCase() === child.targetSchematic.toLowerCase());
	                yield* this.buildSchematicHierarchyInstances(project, context, childHierarchies, hierarchyInstances, [...path, instance], myPathUniqueIdParts, child.sheetSymbol);
	            }
	        }
	        if (path.length === 0) {
	            for (const [_hierarchy, instances] of hierarchyInstances) {
	                for (const instance of instances) {
	                    if (instance.totalSheetInstances !== 0 && instance.totalSheetInstances !== instances.length) {
	                        yield new TransformProcessingResult_1.TransformProcessingResult(project, {
	                            type: 'orcad-dsn-transformer',
	                            message: `Total sheet instances mismatch for ${instance.name}. Expected: ${instances.length}, Found: ${instance.totalSheetInstances}`,
	                            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                            expected: instances.length,
	                            found: instance.totalSheetInstances,
	                        });
	                    }
	                    instance.totalSheetInstances = instances.length;
	                    const lastParent = instance.path[instance.path.length - 1];
	                    if (lastParent) {
	                        lastParent.children.push(instance);
	                    }
	                }
	            }
	        }
	        return hierarchyInstances;
	    }
	    buildDesignHierarchyNodes(allInstances, context) {
	        const rootSourceFileKey = context.designSourceFiles.get(context.rootDesign) ?? '';
	        const byFile = new Map();
	        for (const hierarchy of context.allHierarchies) {
	            const sourceFileKey = context.designSourceFiles.get(hierarchy.design) ?? hierarchy.sourceFile;
	            let bySchematic = byFile.get(sourceFileKey);
	            if (!bySchematic) {
	                bySchematic = new Map();
	                byFile.set(sourceFileKey, bySchematic);
	            }
	            const schName = hierarchy.schematic.Name;
	            const arr = bySchematic.get(schName) ?? [];
	            arr.push(hierarchy);
	            bySchematic.set(schName, arr);
	        }
	        const fileNodes = [];
	        for (const [sourceFileKey, bySchematic] of byFile) {
	            const isRoot = sourceFileKey === rootSourceFileKey;
	            const fileLabel = isRoot ? sigma_engine_shared_1.Files.basenamePath(sourceFileKey) : sourceFileKey;
	            const schematicNodes = [];
	            for (const [schName, hierarchies] of bySchematic) {
	                if (hierarchies.length === 1) {
	                    const pageNode = this.buildPageNode(hierarchies[0], allInstances, context);
	                    schematicNodes.push({
	                        ...pageNode,
	                        label: schName,
	                    });
	                }
	                else {
	                    const pageNodes = [];
	                    for (const hierarchy of hierarchies) {
	                        pageNodes.push(this.buildPageNode(hierarchy, allInstances, context));
	                    }
	                    schematicNodes.push({
	                        label: schName,
	                        children: pageNodes,
	                    });
	                }
	            }
	            fileNodes.push({
	                label: fileLabel,
	                children: schematicNodes,
	            });
	        }
	        return fileNodes;
	    }
	    buildPageNode(hierarchy, allInstances, context) {
	        const instances = allInstances.get(hierarchy) ?? [];
	        if (instances.length > 1) {
	            const instanceChildren = instances.map((inst) => {
	                const instSchematic = context.instanceSchematicMap.get(inst);
	                const instLabel = this.buildInstanceLabel(inst);
	                return {
	                    designId: (0, OrcadDsnUtils_1.orcadDesignId)(hierarchy.name),
	                    label: instLabel,
	                    instanceId: instSchematic?.designId,
	                };
	            });
	            return {
	                designId: (0, OrcadDsnUtils_1.orcadDesignId)(hierarchy.name),
	                label: hierarchy.page.Name,
	                children: instanceChildren,
	            };
	        }
	        const inst = instances[0];
	        const schematic = inst ? context.instanceSchematicMap.get(inst) : undefined;
	        return {
	            designId: schematic?.designId,
	            label: hierarchy.page.Name,
	        };
	    }
	    buildInstanceLabel(inst) {
	        const parts = [...inst.path, inst].map((p) => p.designator).filter(Boolean);
	        return parts.length > 0 ? `(/${parts.join('/')})` : inst.name;
	    }
	    createLayerSet(schematic) {
	        const layers = orcadLayers_1.OrCadSchLayers.map((layer) => ({
	            ...layer,
	            defaultVisible: typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true,
	        }));
	        return new TransformSchematicLayerSet_1.TransformSchematicLayerSet(schematic, {
	            type: 'layerSet',
	            id: orcadLayers_1.ORCAD_SCH_LAYERSET,
	            layerSetType: 'schematic',
	            layers,
	        });
	    }
	}
	OrcadDsnTransformSchematicGenerator.OrCadDsnTransformSchematicGenerator = OrCadDsnTransformSchematicGenerator;
	
	return OrcadDsnTransformSchematicGenerator;
}

var OrcadDsnTransformModuleGenerator = {};

var alphaDesignatorToConstant = {};

var hasRequiredAlphaDesignatorToConstant;

function requireAlphaDesignatorToConstant () {
	if (hasRequiredAlphaDesignatorToConstant) return alphaDesignatorToConstant;
	hasRequiredAlphaDesignatorToConstant = 1;
	Object.defineProperty(alphaDesignatorToConstant, "__esModule", { value: true });
	alphaDesignatorToConstant.alphaDesignatorToIndex = alphaDesignatorToIndex;
	alphaDesignatorToConstant.isAlphaDesignator = isAlphaDesignator;
	alphaDesignatorToConstant.indexToAlphaDesignator = indexToAlphaDesignator;
	function alphaDesignatorToIndex(alpha) {
	    if (!isAlphaDesignator(alpha)) {
	        throw new Error(`Invalid alphabetic designator: ${alpha}`);
	    }
	    return alpha.split('').reduce((p, c) => p * 26 + c.charCodeAt(0) - 'A'.charCodeAt(0) + 1, 0);
	}
	function isAlphaDesignator(designator) {
	    return /^[A-Z]+$/.test(designator);
	}
	function indexToAlphaDesignator(index) {
	    if (!Number.isInteger(index) || index < 1) {
	        throw new Error(`Invalid 1-based alphabetic designator index: ${index}`);
	    }
	    let remaining = index;
	    let designator = '';
	    while (remaining > 0) {
	        remaining--;
	        designator = String.fromCharCode('A'.charCodeAt(0) + (remaining % 26)) + designator;
	        remaining = Math.floor(remaining / 26);
	    }
	    return designator;
	}
	
	return alphaDesignatorToConstant;
}

var OrcadDsnTransformComponentGenerator = {};

var orcadShapeConverters = {};

var hasRequiredOrcadShapeConverters;

function requireOrcadShapeConverters () {
	if (hasRequiredOrcadShapeConverters) return orcadShapeConverters;
	hasRequiredOrcadShapeConverters = 1;
	Object.defineProperty(orcadShapeConverters, "__esModule", { value: true });
	orcadShapeConverters.OrCadShapeConvert = void 0;
	const imageAssets_1 = requireImageAssets();
	var OrCadShapeConvert;
	(function (OrCadShapeConvert) {
	    const defaultNumPoints = 10;
	    const defaultPointTransform = (p) => p;
	    const defaultLineWidthForZero = 1;
	    const setZeroLineWidthToConstant = (lineWidthForZero = defaultLineWidthForZero) => (lineWidth) => lineWidth === undefined || lineWidth === 0 ? lineWidthForZero : lineWidth;
	    function ellipse(opts) {
	        const numPoints = opts?.ellipse?.numPoints ?? defaultNumPoints;
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (ellipse) => {
	            const points = [];
	            const centerX = (ellipse.X1 + ellipse.X2) / 2;
	            const centerY = (ellipse.Y1 + ellipse.Y2) / 2;
	            const radiusX = Math.abs(ellipse.X2 - ellipse.X1) / 2;
	            const radiusY = Math.abs(ellipse.Y2 - ellipse.Y1) / 2;
	            for (let i = 0; i < numPoints; i++) {
	                const angle = (i * Math.PI * 2) / numPoints;
	                const x = centerX + radiusX * Math.cos(angle);
	                const y = centerY + radiusY * Math.sin(angle);
	                points.push(pointTransform([x, y]));
	            }
	            points.push([points[0][0], points[0][1]]);
	            const polygon = {
	                type: 'polygon',
	                filled: ellipse.FillStyle !== 1,
	                width: lineWidth(ellipse.LineWidth),
	                points: points,
	            };
	            return [polygon];
	        };
	    }
	    OrCadShapeConvert.ellipse = ellipse;
	    function rect(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (rectangle) => {
	            const rect = {
	                type: 'rect',
	                start: pointTransform([rectangle.X1, rectangle.Y1]),
	                end: pointTransform([rectangle.X2, rectangle.Y2]),
	                strokeWidth: lineWidth(rectangle.LineWidth),
	                filled: rectangle.FillStyle !== 1,
	            };
	            return [rect];
	        };
	    }
	    OrCadShapeConvert.rect = rect;
	    function line(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const overrideWidth = opts?.line?.overrideWidth;
	        const lineWidth = overrideWidth !== undefined ?
	            (_) => overrideWidth
	            : setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (line) => {
	            const segment = {
	                type: 'segment',
	                start: pointTransform([line.X1, line.Y1]),
	                end: pointTransform([line.X2, line.Y2]),
	                width: lineWidth(line.LineWidth),
	            };
	            return [segment];
	        };
	    }
	    OrCadShapeConvert.line = line;
	    function polyLine(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (polyline) => {
	            const points = polyline.PolylinePoint.map((p) => pointTransform([p.X, p.Y]));
	            return [
	                {
	                    type: 'polygon',
	                    filled: false,
	                    width: lineWidth(polyline.LineWidth),
	                    points: points,
	                },
	            ];
	        };
	    }
	    OrCadShapeConvert.polyLine = polyLine;
	    function polygon(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (polygon) => {
	            const points = polygon.PolygonPoint.map((p) => pointTransform([p.X, p.Y]));
	            const poly = {
	                type: 'polygon',
	                filled: polygon.FillStyle !== 1,
	                width: lineWidth(polygon.LineWidth),
	                points: points,
	            };
	            return [poly];
	        };
	    }
	    OrCadShapeConvert.polygon = polygon;
	    function arc(opts) {
	        const numPoints = opts?.arc?.numPoints ?? defaultNumPoints;
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (arc) => {
	            const cx = (arc.X1 + arc.X2) / 2;
	            const cy = (arc.Y1 + arc.Y2) / 2;
	            const rx = Math.abs(arc.X2 - arc.X1) / 2;
	            const ry = Math.abs(arc.Y2 - arc.Y1) / 2;
	            if (rx === 0 || ry === 0) {
	                return [
	                    {
	                        type: 'segment',
	                        start: pointTransform([arc.StartX, arc.StartY]),
	                        end: pointTransform([arc.EndX, arc.EndY]),
	                        width: lineWidth(arc.LineWidth),
	                    },
	                ];
	            }
	            const startT = Math.atan2((arc.StartY - cy) / ry, (arc.StartX - cx) / rx);
	            const endT = Math.atan2((arc.EndY - cy) / ry, (arc.EndX - cx) / rx);
	            const twoPi = Math.PI * 2;
	            let diff = endT - startT;
	            while (diff <= -Math.PI) {
	                diff += twoPi;
	            }
	            while (diff > Math.PI) {
	                diff -= twoPi;
	            }
	            const points = [];
	            for (let i = 0; i <= numPoints; i++) {
	                const t = startT + (diff * i) / numPoints;
	                const x = cx + rx * Math.cos(t);
	                const y = cy + ry * Math.sin(t);
	                points.push(pointTransform([x, y]));
	            }
	            return [
	                {
	                    type: 'polygon',
	                    filled: false,
	                    width: lineWidth(arc.LineWidth),
	                    points,
	                },
	            ];
	        };
	    }
	    OrCadShapeConvert.arc = arc;
	    function elementWithGeometries(opts) {
	        return (element) => {
	            const drawings = [];
	            element.Ellipse?.forEach((e) => drawings.push(...ellipse(opts)(e)));
	            element.Rectangle?.forEach((r) => drawings.push(...rect(opts)(r)));
	            element.Line?.forEach((l) => drawings.push(...line(opts)(l)));
	            element.Polyline?.forEach((p) => drawings.push(...polyLine(opts)(p)));
	            element.Polygon?.forEach((p) => drawings.push(...polygon(opts)(p)));
	            element.Arc?.forEach((a) => drawings.push(...arc(opts)(a)));
	            return drawings;
	        };
	    }
	    OrCadShapeConvert.elementWithGeometries = elementWithGeometries;
	    function graphicLineInst(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (line) => {
	            const segment = {
	                type: 'segment',
	                start: pointTransform([line.StartX, line.StartY]),
	                end: pointTransform([line.EndX, line.EndY]),
	                width: lineWidth(line.LineWidth),
	            };
	            return [segment];
	        };
	    }
	    OrCadShapeConvert.graphicLineInst = graphicLineInst;
	    function graphicBoxInst(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (box) => {
	            const rect = {
	                type: 'rect',
	                start: pointTransform([box.X1, box.Y1]),
	                end: pointTransform([box.X2, box.Y2]),
	                strokeWidth: lineWidth(box.LineWidth),
	                filled: box.FillStyle !== 1,
	            };
	            return [rect];
	        };
	    }
	    OrCadShapeConvert.graphicBoxInst = graphicBoxInst;
	    function graphicPolylineInst(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (polyline) => {
	            const points = polyline.PolylinePoint.map((p) => pointTransform([p.X, p.Y]));
	            return [
	                {
	                    type: 'polygon',
	                    filled: false,
	                    width: lineWidth(polyline.LineWidth),
	                    points: points,
	                },
	            ];
	        };
	    }
	    OrCadShapeConvert.graphicPolylineInst = graphicPolylineInst;
	    function graphicPolygonInst(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
	        return (polygon) => {
	            const points = polygon.PolygonPoint.map((p) => pointTransform([p.X, p.Y]));
	            const poly = {
	                type: 'polygon',
	                filled: polygon.FillStyle !== 1,
	                width: lineWidth(polygon.LineWidth),
	                points: points,
	            };
	            return [poly];
	        };
	    }
	    OrCadShapeConvert.graphicPolygonInst = graphicPolygonInst;
	    function elementBitmaps(opts) {
	        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
	        return (element) => {
	            const drawings = [];
	            const imageExtractions = [];
	            for (const bitmap of element.Bitmap ?? []) {
	                if (!bitmap.Value) {
	                    continue;
	                }
	                const { data, mimeType } = (0, imageAssets_1.decodeBase64Image)(bitmap.Value);
	                const ref = (0, imageAssets_1.computeImageRef)(`symbol-bitmap-${bitmap.Value.substring(0, 64)}`, mimeType);
	                const pos = pointTransform([bitmap.LocX, bitmap.LocY]);
	                const corner = pointTransform([
	                    bitmap.LocX + Math.abs(bitmap.X2 - bitmap.X1),
	                    bitmap.LocY + Math.abs(bitmap.Y2 - bitmap.Y1),
	                ]);
	                const width = Math.abs(corner[0] - pos[0]);
	                const height = Math.abs(corner[1] - pos[1]);
	                drawings.push({
	                    type: 'image',
	                    pos: [Math.min(pos[0], corner[0]), Math.min(pos[1], corner[1])],
	                    width,
	                    height,
	                    imageRef: ref,
	                });
	                imageExtractions.push({ ref, name: `symbol-bitmap`, data, mimeType });
	            }
	            return { drawings, imageExtractions };
	        };
	    }
	    OrCadShapeConvert.elementBitmaps = elementBitmaps;
	})(OrCadShapeConvert || (orcadShapeConverters.OrCadShapeConvert = OrCadShapeConvert = {}));
	
	return orcadShapeConverters;
}

var OrcadDsnPhysicalParts = {};

var OrcadDsnSectionNumbering = {};

var hasRequiredOrcadDsnSectionNumbering;

function requireOrcadDsnSectionNumbering () {
	if (hasRequiredOrcadDsnSectionNumbering) return OrcadDsnSectionNumbering;
	hasRequiredOrcadDsnSectionNumbering = 1;
	Object.defineProperty(OrcadDsnSectionNumbering, "__esModule", { value: true });
	OrcadDsnSectionNumbering.orcadDsnNumberingStyleFromFlag = orcadDsnNumberingStyleFromFlag;
	OrcadDsnSectionNumbering.orcadDsnSectionDesignator = orcadDsnSectionDesignator;
	OrcadDsnSectionNumbering.orcadDsnSignalIdSectionSuffix = orcadDsnSignalIdSectionSuffix;
	OrcadDsnSectionNumbering.orcadDsnSectionPlacementName = orcadDsnSectionPlacementName;
	const alphaDesignatorToConstant_1 = requireAlphaDesignatorToConstant();
	function orcadDsnNumberingStyleFromFlag(alphabeticNumbering) {
	    return alphabeticNumbering === 1 ? 'alphabetic' : 'numeric';
	}
	function orcadDsnSectionDesignator(index, style) {
	    if (style === 'alphabetic') {
	        return (0, alphaDesignatorToConstant_1.indexToAlphaDesignator)(index + 1);
	    }
	    return `${index + 1}`;
	}
	function orcadDsnSignalIdSectionSuffix(index, style) {
	    return `@s${orcadDsnSectionDesignator(index, style)}`;
	}
	function orcadDsnSectionPlacementName(bareReference, index, style) {
	    const designator = orcadDsnSectionDesignator(index, style);
	    return style === 'alphabetic' ? `${bareReference}${designator}` : `${bareReference}-${designator}`;
	}
	
	return OrcadDsnSectionNumbering;
}

var hasRequiredOrcadDsnPhysicalParts;

function requireOrcadDsnPhysicalParts () {
	if (hasRequiredOrcadDsnPhysicalParts) return OrcadDsnPhysicalParts;
	hasRequiredOrcadDsnPhysicalParts = 1;
	Object.defineProperty(OrcadDsnPhysicalParts, "__esModule", { value: true });
	OrcadDsnPhysicalParts.OrcadDsnPhysicalPartResolver = void 0;
	OrcadDsnPhysicalParts.resolveEmptyDesignatorPhysicalPartBijection = resolveEmptyDesignatorPhysicalPartBijection;
	OrcadDsnPhysicalParts.getPhysicalPartComponentName = getPhysicalPartComponentName;
	OrcadDsnPhysicalParts.findUnificationConflict = findUnificationConflict;
	OrcadDsnPhysicalParts.findNumberingStyleDisagreement = findNumberingStyleDisagreement;
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const OrcadDsnSectionNumbering_1 = requireOrcadDsnSectionNumbering();
	function resolveEmptyDesignatorPhysicalPartBijection(placements, physicalParts) {
	    const result = new Map();
	    const empty = placements.filter((placement) => placement.deviceDesignator === '');
	    if (empty.length < 2) {
	        return result;
	    }
	    if (empty.length !== physicalParts.length) {
	        for (const placement of empty) {
	            result.set(placement.key, {
	                kind: 'declined',
	                message: `[empty-designator-physical-part-declined] placements=${empty.length} sections=${physicalParts.length} reason=incomplete-bijection`,
	            });
	        }
	        return result;
	    }
	    const maps = physicalParts.map((part) => part.PinNumber ?? []);
	    const portSurface = empty[0]?.portSurface;
	    if (!portSurface ||
	        empty.some((placement) => placement.portSurface !== portSurface) ||
	        !haveIdenticalOrdinalSurfaces(maps) ||
	        !arePairwiseDisjointPhysicalParts(maps)) {
	        for (const placement of empty) {
	            result.set(placement.key, {
	                kind: 'declined',
	                message: `[empty-designator-physical-part-declined] placements=${empty.length} sections=${physicalParts.length} reason=non-isomorphic-or-overlapping-pin-maps`,
	            });
	        }
	        return result;
	    }
	    const byDbId = [...empty].sort((left, right) => left.dbId - right.dbId || left.key.localeCompare(right.key));
	    const byPage = [...empty].sort((left, right) => left.pageOrdinal - right.pageOrdinal || left.dbId - right.dbId || left.key.localeCompare(right.key));
	    if (byDbId.some((placement, index) => placement.key !== byPage[index]?.key)) {
	        for (const placement of empty) {
	            result.set(placement.key, {
	                kind: 'declined',
	                message: `[empty-designator-physical-part-declined] placements=${empty.length} sections=${physicalParts.length} reason=order-key-disagreement globalDbId=${byDbId.map((item) => item.dbId).join(',')} pageMajor=${byPage.map((item) => `${item.pageOrdinal}:${item.dbId}`).join(',')}`,
	            });
	        }
	        return result;
	    }
	    for (const [physicalPartIndex, placement] of byDbId.entries()) {
	        result.set(placement.key, {
	            kind: 'inferred',
	            physicalPartIndex,
	            message: `[empty-designator-physical-part-inferred] placement=${placement.key} physicalPartIndex=${physicalPartIndex + 1} order=global-dbId/page-major`,
	        });
	    }
	    return result;
	}
	function haveIdenticalOrdinalSurfaces(maps) {
	    const surface = maps[0]?.map((pin) => pin.Position);
	    return (!!surface &&
	        maps.every((map) => map.length === surface.length && map.every((pin, index) => pin.Position === surface[index])));
	}
	function arePairwiseDisjointPhysicalParts(maps) {
	    for (let left = 0; left < maps.length; left++) {
	        const seen = new Set(maps[left].map((pin) => String(pin.Number)));
	        for (let right = left + 1; right < maps.length; right++) {
	            if (maps[right].some((pin) => seen.has(String(pin.Number)))) {
	                return false;
	            }
	        }
	    }
	    return true;
	}
	const AMBIGUOUS_OCCURRENCE_REFERENCE = Symbol('ambiguous-occurrence-reference');
	const AMBIGUOUS_KEY_PREFIX = '\0orcad-ambiguous-occurrence-reference\0';
	class OrcadDsnPhysicalPartResolver {
	    constructor() {
	        this.indexCache = new Map();
	        this.occurrenceReferenceCache = new Map();
	    }
	    getPhysicalPartReference(design, partInst) {
	        const dbId = partInst.DbId.toString();
	        const occurrenceReference = this.getOccurrenceReferenceMap(design).get(dbId);
	        if (occurrenceReference === AMBIGUOUS_OCCURRENCE_REFERENCE) {
	            return `${AMBIGUOUS_KEY_PREFIX}${dbId}`;
	        }
	        return occurrenceReference ?? partInst.Reference;
	    }
	    getIndexForDesign(design) {
	        const cached = this.indexCache.get(design);
	        if (cached) {
	            return cached;
	        }
	        const index = this.buildPhysicalPartIndex(design);
	        this.indexCache.set(design, index);
	        return index;
	    }
	    getOccurrenceReferenceMap(design) {
	        const cached = this.occurrenceReferenceCache.get(design);
	        if (cached) {
	            return cached;
	        }
	        const referencesByDbId = new Map();
	        const visit = (nodes) => {
	            for (const node of nodes ?? []) {
	                const partReference = node.UserProperty?.find((property) => property.Name === 'Part Reference')?.Value;
	                if (node.RefInstId && partReference !== undefined) {
	                    const existing = referencesByDbId.get(node.RefInstId);
	                    if (existing !== undefined && existing !== partReference) {
	                        referencesByDbId.set(node.RefInstId, AMBIGUOUS_OCCURRENCE_REFERENCE);
	                    }
	                    else {
	                        referencesByDbId.set(node.RefInstId, partReference);
	                    }
	                }
	                visit(node.InstOccurrence);
	            }
	        };
	        visit(design.Occurrences?.InstOccurrence);
	        this.occurrenceReferenceCache.set(design, referencesByDbId);
	        return referencesByDbId;
	    }
	    buildPhysicalPartIndex(design) {
	        const sectionsByReference = new Map();
	        for (const schematic of design.Schematic ?? []) {
	            for (const page of schematic.Page ?? []) {
	                for (const partInst of page.PartInst ?? []) {
	                    if (partInst.ContentsViewType === 1 && partInst.ContentsLibName) {
	                        continue;
	                    }
	                    const reference = this.getPhysicalPartReference(design, partInst);
	                    if (!reference || reference.includes('?')) {
	                        continue;
	                    }
	                    const sectionKey = `${partInst.LibName}\0${partInst.PkgName}`;
	                    let sections = sectionsByReference.get(reference);
	                    if (!sections) {
	                        sections = new Map();
	                        sectionsByReference.set(reference, sections);
	                    }
	                    if (!sections.has(sectionKey)) {
	                        sections.set(sectionKey, {
	                            libName: partInst.LibName,
	                            packageName: partInst.PkgName,
	                            representativePartInst: partInst,
	                        });
	                    }
	                }
	            }
	        }
	        const index = { parts: new Map(), conflicts: [] };
	        for (const [reference, sectionMap] of sectionsByReference) {
	            if (sectionMap.size < 2) {
	                continue;
	            }
	            const sections = Array.from(sectionMap.values());
	            const conflict = findUnificationConflict(design.Cache, reference, sections);
	            if (conflict) {
	                index.conflicts.push(conflict);
	                continue;
	            }
	            index.parts.set(reference, {
	                reference,
	                sections,
	                componentName: getPhysicalPartComponentName(sections),
	            });
	        }
	        return index;
	    }
	}
	OrcadDsnPhysicalParts.OrcadDsnPhysicalPartResolver = OrcadDsnPhysicalPartResolver;
	function getPhysicalPartComponentName(sections) {
	    const sameLib = sections.every((s) => s.libName === sections[0].libName);
	    const names = sections.map((s) => (sameLib ? s.packageName : `${s.libName}:${s.packageName}`)).sort();
	    const prefix = longestCommonPrefix(names);
	    const grouped = `${prefix}{${names.map((n) => n.slice(prefix.length)).join(',')}}`;
	    return sameLib ? `${sections[0].libName}:${grouped}` : grouped;
	}
	function longestCommonPrefix(sortedNames) {
	    const first = sortedNames[0];
	    const last = sortedNames[sortedNames.length - 1];
	    let length = 0;
	    while (length < first.length && length < last.length && first[length] === last[length]) {
	        length++;
	    }
	    return first.slice(0, length);
	}
	function findUnificationConflict(cache, reference, sections) {
	    const symbolRefOwners = new Map();
	    const pinNumbersBySection = [];
	    for (const section of sections) {
	        const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
	        if (!resolution) {
	            return {
	                reference,
	                message: `Package ${section.libName}:${section.packageName} for reference ${reference} not found in design cache`,
	            };
	        }
	        const sectionName = `${section.libName}:${section.packageName}`;
	        pinNumbersBySection.push({ sectionName, ...collectSectionPinNumbers(resolution.pkg) });
	        for (const symbolRef of enumerateSymbolRefs(resolution.pkg)) {
	            const owner = symbolRefOwners.get(symbolRef);
	            if (owner !== undefined && owner !== sectionName) {
	                return {
	                    reference,
	                    message: `Sections ${owner} and ${sectionName} of reference ${reference} both define symbol ${symbolRef}`,
	                };
	            }
	            symbolRefOwners.set(symbolRef, sectionName);
	        }
	    }
	    for (const section of pinNumbersBySection) {
	        for (const duplicated of section.duplicated) {
	            const other = pinNumbersBySection.find((s) => s !== section && s.numbers.has(duplicated));
	            if (other) {
	                return {
	                    reference,
	                    message: `Pin number ${duplicated} is assigned to more than one pin in section ${section.sectionName} and also appears in section ${other.sectionName} of reference ${reference}, so pins cannot be safely matched across packages`,
	                };
	            }
	        }
	    }
	    return undefined;
	}
	function findNumberingStyleDisagreement(cache, sections) {
	    let chosen;
	    for (const section of sections) {
	        const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
	        if (!resolution) {
	            continue;
	        }
	        const style = (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(resolution.pkg.AlphabeticNumbering);
	        const sectionName = `${section.libName}:${section.packageName}`;
	        if (!chosen) {
	            chosen = { style, sectionName };
	        }
	        else if (style !== chosen.style) {
	            return {
	                chosenStyle: chosen.style,
	                chosenSectionName: chosen.sectionName,
	                dissentingStyle: style,
	                dissentingSectionName: sectionName,
	            };
	        }
	    }
	    return undefined;
	}
	function collectSectionPinNumbers(pkg) {
	    const numbers = new Set();
	    const duplicated = new Set();
	    for (const libPart of pkg.LibPart ?? []) {
	        for (const physicalPart of libPart.PhysicalPart ?? []) {
	            const seen = new Set();
	            for (const pinNumber of physicalPart.PinNumber ?? []) {
	                const number = pinNumber.Number.toString();
	                if (seen.has(number)) {
	                    duplicated.add(number);
	                }
	                seen.add(number);
	                numbers.add(number);
	            }
	        }
	    }
	    return { numbers, duplicated };
	}
	function enumerateSymbolRefs(pkg) {
	    const refs = [];
	    for (const libPart of pkg.LibPart ?? []) {
	        for (const normalView of libPart.NormalView ?? []) {
	            const physicalParts = libPart.PhysicalPart ?? [];
	            if (physicalParts.length > 1) {
	                for (let physIdx = 0; physIdx < physicalParts.length; physIdx++) {
	                    refs.push(`${libPart.CellName}${normalView.Suffix}-${physIdx + 1}`);
	                }
	            }
	            else {
	                refs.push(`${libPart.CellName}${normalView.Suffix}`);
	            }
	        }
	    }
	    return refs;
	}
	
	return OrcadDsnPhysicalParts;
}

var orcadPinRefs = {};

var hasRequiredOrcadPinRefs;

function requireOrcadPinRefs () {
	if (hasRequiredOrcadPinRefs) return orcadPinRefs;
	hasRequiredOrcadPinRefs = 1;
	Object.defineProperty(orcadPinRefs, "__esModule", { value: true });
	orcadPinRefs.buildPinRefByPosition = buildPinRefByPosition;
	orcadPinRefs.duplicateNumericPinNumbers = duplicateNumericPinNumbers;
	function buildPinRefByPosition(pinNumberByPosition) {
	    const positionsByNumber = new Map();
	    for (const [position, pinNumber] of pinNumberByPosition) {
	        let positions = positionsByNumber.get(pinNumber);
	        if (!positions) {
	            positions = [];
	            positionsByNumber.set(pinNumber, positions);
	        }
	        positions.push(position);
	    }
	    const refByPosition = new Map();
	    for (const [position, pinNumber] of pinNumberByPosition) {
	        refByPosition.set(position, pinNumber);
	    }
	    const usedRefs = new Set(pinNumberByPosition.values());
	    for (const [pinNumber, positions] of positionsByNumber) {
	        if (positions.length < 2) {
	            continue;
	        }
	        const sorted = [...positions].sort((a, b) => a - b);
	        for (const position of sorted.slice(1)) {
	            let candidate;
	            candidate = `${pinNumber}@${position}`;
	            while (usedRefs.has(candidate)) {
	                candidate = `${candidate}@${position}`;
	            }
	            usedRefs.add(candidate);
	            refByPosition.set(position, candidate);
	        }
	    }
	    return refByPosition;
	}
	function duplicateNumericPinNumbers(pinNumberByPosition) {
	    const counts = new Map();
	    for (const pinNumber of pinNumberByPosition.values()) {
	        if (/^\d+$/.test(pinNumber)) {
	            counts.set(pinNumber, (counts.get(pinNumber) ?? 0) + 1);
	        }
	    }
	    return [...counts].filter(([, count]) => count > 1).map(([pinNumber]) => pinNumber);
	}
	
	return orcadPinRefs;
}

var OrcadDsnSectionEmission = {};

var hasRequiredOrcadDsnSectionEmission;

function requireOrcadDsnSectionEmission () {
	if (hasRequiredOrcadDsnSectionEmission) return OrcadDsnSectionEmission;
	hasRequiredOrcadDsnSectionEmission = 1;
	Object.defineProperty(OrcadDsnSectionEmission, "__esModule", { value: true });
	OrcadDsnSectionEmission.buildPinPositionMap = buildPinPositionMap;
	OrcadDsnSectionEmission.claimPinsForPhysicalPart = claimPinsForPhysicalPart;
	OrcadDsnSectionEmission.physicalPartEmitsPins = physicalPartEmitsPins;
	OrcadDsnSectionEmission.enumerateGenuineSections = enumerateGenuineSections;
	const orcadPinRefs_1 = requireOrcadPinRefs();
	function buildPinPositionMap(physicalPart) {
	    const pinPositionMap = new Map();
	    for (const pinNumber of physicalPart.PinNumber ?? []) {
	        if (pinPositionMap.has(pinNumber.Position)) {
	            throw new Error(`Duplicate pin position ${pinNumber.Position} found in physical part`);
	        }
	        const number = pinNumber.Number.toString();
	        if (number === '') {
	            throw new Error(`Empty pin number at position ${pinNumber.Position} in physical part`);
	        }
	        pinPositionMap.set(pinNumber.Position, number);
	    }
	    return pinPositionMap;
	}
	function claimPinsForPhysicalPart(libPart, pinPositionMap, pinRefByPosition, emittedPinNumbersByView) {
	    const claimedByView = new Map();
	    for (const normalView of libPart.NormalView ?? []) {
	        let emittedPinNumbers = emittedPinNumbersByView.get(normalView.Suffix);
	        if (!emittedPinNumbers) {
	            emittedPinNumbers = new Set();
	            emittedPinNumbersByView.set(normalView.Suffix, emittedPinNumbers);
	        }
	        const claimed = new Set();
	        for (const p of [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])]) {
	            const pinNumber = pinPositionMap.get(p.Position);
	            if (pinNumber === undefined) {
	                throw new Error(`Pin position ${p.Position} not found in pin position map`);
	            }
	            const pinRef = pinRefByPosition.get(p.Position) ?? pinNumber;
	            if (emittedPinNumbers.has(pinRef)) {
	                continue;
	            }
	            emittedPinNumbers.add(pinRef);
	            claimed.add(pinRef);
	        }
	        claimedByView.set(normalView.Suffix, claimed);
	    }
	    return claimedByView;
	}
	function physicalPartEmitsPins(libPart, physicalPart, emittedPinNumbersByView) {
	    const pinPositionMap = buildPinPositionMap(physicalPart);
	    const pinRefByPosition = (0, orcadPinRefs_1.buildPinRefByPosition)(pinPositionMap);
	    const claimed = claimPinsForPhysicalPart(libPart, pinPositionMap, pinRefByPosition, emittedPinNumbersByView);
	    return Array.from(claimed.values()).some((pinRefs) => pinRefs.size > 0);
	}
	function enumerateGenuineSections(libPartsInEmissionOrder, claimScope) {
	    const sections = [];
	    const sharedClaims = new Map();
	    for (const libPart of libPartsInEmissionOrder) {
	        const claims = claimScope === 'perLibPart' ? new Map() : sharedClaims;
	        for (const physicalPart of libPart.PhysicalPart ?? []) {
	            if (physicalPartEmitsPins(libPart, physicalPart, claims)) {
	                sections.push({ libPart, physicalPart });
	            }
	        }
	    }
	    return sections;
	}
	
	return OrcadDsnSectionEmission;
}

var orcadDsnPhysicalPartFallback = {};

var hasRequiredOrcadDsnPhysicalPartFallback;

function requireOrcadDsnPhysicalPartFallback () {
	if (hasRequiredOrcadDsnPhysicalPartFallback) return orcadDsnPhysicalPartFallback;
	hasRequiredOrcadDsnPhysicalPartFallback = 1;
	Object.defineProperty(orcadDsnPhysicalPartFallback, "__esModule", { value: true });
	orcadDsnPhysicalPartFallback.synthesizePhysicalPartFromPins = synthesizePhysicalPartFromPins;
	function synthesizePhysicalPartFromPins(libPart) {
	    const normalView = libPart.NormalView?.[0];
	    const pins = [...(normalView?.PinScalar ?? []), ...(normalView?.PinBus ?? [])];
	    return {
	        PinNumber: pins.map((pin) => ({
	            Position: pin.Position,
	            Number: pin.Name,
	        })),
	    };
	}
	
	return orcadDsnPhysicalPartFallback;
}

var hasRequiredOrcadDsnTransformComponentGenerator;

function requireOrcadDsnTransformComponentGenerator () {
	if (hasRequiredOrcadDsnTransformComponentGenerator) return OrcadDsnTransformComponentGenerator;
	hasRequiredOrcadDsnTransformComponentGenerator = 1;
	Object.defineProperty(OrcadDsnTransformComponentGenerator, "__esModule", { value: true });
	OrcadDsnTransformComponentGenerator.OrCadDsnTransformComponentGenerator = OrcadDsnTransformComponentGenerator.OrCadDsnTransformComponent = OrcadDsnTransformComponentGenerator.OrCadDsnTransformComponentRequest = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const Transform_1 = requireTransform();
	const TransformObject_1 = requireTransformObject();
	const TransformComponent_1 = requireTransformComponent();
	const TransformComponentSymbol_1 = requireTransformComponentSymbol();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const orcadLayers_1 = requireOrcadLayers();
	const orcadShapeConverters_1 = requireOrcadShapeConverters();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const OrcadDsnPhysicalParts_1 = requireOrcadDsnPhysicalParts();
	const orcadPinRefs_1 = requireOrcadPinRefs();
	const OrcadDsnSectionNumbering_1 = requireOrcadDsnSectionNumbering();
	const OrcadDsnSectionEmission_1 = requireOrcadDsnSectionEmission();
	const orcadDsnPhysicalPartFallback_1 = requireOrcadDsnPhysicalPartFallback();
	const ModelText_1 = requireModelText();
	class OrCadDsnTransformComponentRequest extends TransformObject_1.TransformObject {
	    constructor(parent, args) {
	        const componentName = OrCadDsnTransformComponent.GetComponentName(args);
	        const symbolName = OrCadDsnTransformComponent.GetSymbolName(args);
	        super(parent, { ...args, componentName, symbolName }, `${componentName}::${symbolName}`, new Set());
	        this.dependency = new OrCadDsnTransformComponent(parent, args);
	    }
	    get componentName() {
	        return this._sharedData.componentName;
	    }
	    get symbolName() {
	        return this._sharedData.symbolName;
	    }
	    get dependencies() {
	        return [this.dependency];
	    }
	    compile() {
	        const symbolName = this._sharedData.symbolName;
	        const symbol = this.dependency.compiled.symbols.find((s) => s.name === symbolName);
	        if (!symbol) {
	            throw new Error(`Symbol ${symbolName} not found in component ${this.dependency.compiled.name}`);
	        }
	        return {
	            component: this.dependency.compiled,
	            symbol: symbol,
	        };
	    }
	}
	OrcadDsnTransformComponentGenerator.OrCadDsnTransformComponentRequest = OrCadDsnTransformComponentRequest;
	class OrCadDsnTransformComponent extends TransformComponent_1.TransformComponent {
	    constructor(parent, args) {
	        const physicalPartSections = args.physicalPartSections && args.physicalPartSections.length > 1 ? args.physicalPartSections : undefined;
	        super(parent, {
	            ...args,
	            baseComponent: {
	                name: OrCadDsnTransformComponent.GetComponentName(args),
	                ...(physicalPartSections ?
	                    {
	                        metadata: {
	                            'orcad.physicalPart.sectionCount': physicalPartSections.length,
	                            'orcad.physicalPart.packages': physicalPartSections
	                                .map((s) => `${s.libName}:${s.packageName}`)
	                                .sort()
	                                .join(', '),
	                        },
	                    }
	                    : {}),
	            },
	        });
	    }
	    static GetComponentName(args) {
	        if (args.physicalPartSections && args.physicalPartSections.length > 1) {
	            return (0, OrcadDsnPhysicalParts_1.getPhysicalPartComponentName)(args.physicalPartSections);
	        }
	        return `${args.libName}:${args.packageName}`;
	    }
	    static GetSymbolName(args) {
	        return `${args.libPartCellName}:${args.viewName}:${args.viewSuffix}:${args.physicalPartSuffix}`;
	    }
	    get libName() {
	        return this._sharedData.libName;
	    }
	    get packageName() {
	        return this._sharedData.packageName;
	    }
	    get sourceDesign() {
	        return this._sharedData.sourceDesign;
	    }
	    get packageIndex() {
	        return this._sharedData.packageIndex;
	    }
	    get physicalPartSections() {
	        return this._sharedData.physicalPartSections;
	    }
	}
	OrcadDsnTransformComponentGenerator.OrCadDsnTransformComponent = OrCadDsnTransformComponent;
	class OrCadDsnTransformComponentGenerator {
	    constructor() {
	        this.runsFor = [OrCadDsnTransformComponent];
	    }
	    *generate(parent, _rootContext) {
	        const { libName, packageName } = parent;
	        const cache = parent.sourceDesign.Cache;
	        if (!cache) {
	            throw new Error(`No cache found in design for component ${libName}::${packageName}`);
	        }
	        const sectionCounter = { index: 0, total: this.countTotalSections(parent, cache) };
	        const sections = parent.physicalPartSections;
	        if (sections && sections.length > 1) {
	            const resolutions = sections.map((section) => {
	                const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
	                if (!resolution) {
	                    throw new Error(`Package ${section.libName}::${section.packageName} not found in cache for physical part component ${parent.deduplicationKey}`);
	                }
	                return resolution;
	            });
	            const numberingStyle = (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(resolutions[0].pkg.AlphabeticNumbering);
	            const emittedPinNumbersByView = new Map();
	            for (const resolution of resolutions) {
	                for (const libPart of resolution.pkg.LibPart) {
	                    yield* this.generateSymbolsFromLibPart(parent, resolution.pkg, libPart, sectionCounter, emittedPinNumbersByView, numberingStyle);
	                }
	            }
	            return;
	        }
	        const pkg = OrcadDsnUtils_1.OrcadDsnUtils.findPackageByIdentity(cache, libName, packageName, parent.packageIndex);
	        if (!pkg) {
	            throw new Error(`Package ${libName}::${packageName} not found in cache`);
	        }
	        for (const libPart of pkg.LibPart) {
	            yield* this.generateSymbolsFromLibPart(parent, pkg, libPart, sectionCounter);
	        }
	    }
	    countTotalSections(parent, cache) {
	        const sections = parent.physicalPartSections;
	        if (sections && sections.length > 1) {
	            const libPartsInOrder = [];
	            for (const section of sections) {
	                const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
	                if (!resolution) {
	                    continue;
	                }
	                libPartsInOrder.push(...resolution.pkg.LibPart);
	            }
	            return (0, OrcadDsnSectionEmission_1.enumerateGenuineSections)(libPartsInOrder, 'shared').length;
	        }
	        const pkg = OrcadDsnUtils_1.OrcadDsnUtils.findPackageByIdentity(cache, parent.libName, parent.packageName, parent.packageIndex);
	        if (!pkg) {
	            return 0;
	        }
	        return (0, OrcadDsnSectionEmission_1.enumerateGenuineSections)(pkg.LibPart.map((libPart) => libPart.PhysicalPart?.length ?
	            libPart
	            : { ...libPart, PhysicalPart: [(0, orcadDsnPhysicalPartFallback_1.synthesizePhysicalPartFromPins)(libPart)] }), 'perLibPart').length;
	    }
	    *generateSymbolsFromLibPart(parent, pkg, libPart, sectionCounter, sharedEmittedPinNumbersByView, numberingStyleOverride) {
	        const physicalParts = libPart.PhysicalPart?.length ? libPart.PhysicalPart : [(0, orcadDsnPhysicalPartFallback_1.synthesizePhysicalPartFromPins)(libPart)];
	        if (!libPart.PhysicalPart?.length) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-physical-part-missing',
	                message: `Synthesized a physical part for ${pkg.SourceLibName}::${pkg.Name}::${libPart.CellName}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        const emittedPinNumbersByView = sharedEmittedPinNumbersByView ?? new Map();
	        for (const physicalPart of physicalParts) {
	            const physicalPartIndex = physicalParts.indexOf(physicalPart);
	            const physicalPartSuffix = physicalParts.length > 1 ? `-${physicalPartIndex + 1}` : '';
	            const pinPositionMap = (0, OrcadDsnSectionEmission_1.buildPinPositionMap)(physicalPart);
	            const pinRefByPosition = (0, orcadPinRefs_1.buildPinRefByPosition)(pinPositionMap);
	            const duplicateNumericPins = (0, orcadPinRefs_1.duplicateNumericPinNumbers)(pinPositionMap);
	            if (duplicateNumericPins.length > 0) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-duplicate-numeric-pin-number',
	                    message: `Duplicate numeric pin number(s) ${duplicateNumericPins.join(', ')} in ${pkg.SourceLibName}::${pkg.Name}::${libPart.CellName}; Capture refuses these at netlist time (ORCAP-36035/ORCAP-36037).`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            const claimedPinRefsByView = (0, OrcadDsnSectionEmission_1.claimPinsForPhysicalPart)(libPart, pinPositionMap, pinRefByPosition, emittedPinNumbersByView);
	            const emitsAnyPin = Array.from(claimedPinRefsByView.values()).some((claimed) => claimed.size > 0);
	            const sectionIndex = emitsAnyPin ? sectionCounter.index++ : undefined;
	            const signalIdSuffix = sectionIndex !== undefined && sectionCounter.total > 1 ?
	                (0, OrcadDsnSectionNumbering_1.orcadDsnSignalIdSectionSuffix)(sectionIndex, numberingStyleOverride ?? (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(pkg.AlphabeticNumbering))
	                : undefined;
	            for (const normalView of libPart.NormalView ?? []) {
	                const allowedPinRefs = claimedPinRefsByView.get(normalView.Suffix) ?? new Set();
	                const { symbol, imageExtractions } = this.generateSymbolFromNormalView(parent, libPart, normalView, physicalPartIndex, physicalPartSuffix, pinPositionMap, pinRefByPosition, allowedPinRefs, signalIdSuffix);
	                yield symbol;
	                yield* imageExtractions;
	            }
	        }
	    }
	    generateSymbolFromNormalView(parent, libPart, normalView, physicalPartIndex, physicalPartSuffix, pinPositionMap, pinRefByPosition, allowedPinRefs, signalIdSuffix) {
	        const ref = `${libPart.CellName}${normalView.Suffix}${physicalPartSuffix}`;
	        const bboxCenter = [
	            (normalView.BoundingBox.X1 + normalView.BoundingBox.X2) / 2,
	            (normalView.BoundingBox.Y1 + normalView.BoundingBox.Y2) / 2,
	        ];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
	        const bitmapResult = orcadShapeConverters_1.OrCadShapeConvert.elementBitmaps({ pointTransform })(normalView);
	        const imageExtractions = bitmapResult.imageExtractions.map((ie) => new TransformImageExtraction_1.TransformImageExtraction(parent, ie.ref, ie.name, ie.data, ie.mimeType));
	        const drawings = [
	            ...orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(normalView).map((drawing) => ({
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
	                drawing,
	            })),
	            ...bitmapResult.drawings.map((drawing) => ({
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Image].id],
	                drawing,
	            })),
	            ...(normalView.CommentText ?? []).map((c) => ({
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
	                drawing: {
	                    type: 'text',
	                    ...(0, ModelText_1.encodeStrippedText)({ raw: c.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
	                    pos: pointTransform([c.LocX, c.LocY]),
	                    angle: c.Rotation ?? 0,
	                    justify: [-1, 0],
	                    size: this.scaleFont(c.Font),
	                    thickness: 1,
	                },
	            })),
	        ];
	        const pins = [];
	        for (const p of [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])]) {
	            const pinNumber = pinPositionMap.get(p.Position);
	            if (pinNumber === undefined) {
	                throw new Error(`Pin position ${p.Position} not found in pin position map`);
	            }
	            const pinRef = pinRefByPosition.get(p.Position) ?? pinNumber;
	            if (!allowedPinRefs.has(pinRef)) {
	                continue;
	            }
	            const pinMetadata = {
	                ['orcad.pin.position']: p.Position,
	            };
	            if (p.IsNoConnect) {
	                pinMetadata['orcad.pin.isNoConnect'] = true;
	            }
	            pins.push({
	                type: 'pin',
	                ref: pinRef,
	                name: p.Name,
	                number: pinNumber,
	                start: pointTransform([p.StartX, p.StartY]),
	                end: pointTransform([p.HotptX, p.HotptY]),
	                layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
	                width: 1,
	                drawings: this.generatePinText(p, pinNumber, pointTransform, normalView),
	                metadata: pinMetadata,
	            });
	        }
	        const metadata = {
	            'orcad.symbol.boundingbox': normalView.BoundingBox,
	        };
	        if (physicalPartSuffix) {
	            metadata['orcad.symbol.physicalPartIndex'] = physicalPartIndex;
	        }
	        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(parent, {
	            ref,
	            name: ref,
	            pins,
	            drawings,
	            metadata,
	        });
	        for (const pin of pins) {
	            const signalName = normalView.PinScalar?.find((ps) => ps.Name === pin.name)?.Name ??
	                normalView.PinBus?.find((pb) => pb.Name === pin.name)?.Name;
	            if (signalName) {
	                symbol.setPinSignal(pin, {
	                    id: signalIdSuffix ? `${signalName}${signalIdSuffix}` : signalName,
	                    name: signalName,
	                });
	            }
	        }
	        return { symbol, imageExtractions };
	    }
	    generatePinText(p, pinRef, pointTransform, view) {
	        let nameJustify;
	        let angle;
	        if (p.StartX > p.HotptX) {
	            nameJustify = [-1, 0];
	            angle = 0;
	        }
	        else if (p.StartX < p.HotptX) {
	            nameJustify = [1, 0];
	            angle = 0;
	        }
	        else {
	            if (p.StartY > p.HotptY) {
	                nameJustify = [1, 0];
	                angle = 90;
	            }
	            else {
	                nameJustify = [-1, 0];
	                angle = 90;
	            }
	        }
	        const textSize = 4;
	        const pinNameSource = { raw: p.Name, dialects: [ModelText_1.TextDialect.Orcad] };
	        const estNameTextWidth = (0, ModelText_1.displayLength)(pinNameSource) * textSize;
	        const nameTextOffset = estNameTextWidth / 2;
	        let nameOffset = [0, 0];
	        let pinRefOffset = [0, 0];
	        const pinNumberPerpOffset = textSize;
	        switch (angle) {
	            case 0:
	                nameOffset = [nameTextOffset * -nameJustify[0], 0];
	                pinRefOffset = [0, -pinNumberPerpOffset];
	                break;
	            case 90:
	                nameOffset = [0, nameTextOffset * nameJustify[0]];
	                pinRefOffset = [-pinNumberPerpOffset, 0];
	                break;
	        }
	        const drawings = [];
	        if (view.IsPinNamesVisible) {
	            drawings.push({
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinName].id],
	                drawing: {
	                    type: 'text',
	                    ...(0, ModelText_1.encodeStrippedText)(pinNameSource),
	                    pos: (0, Transform_1.translatePoint)(pointTransform([p.StartX, p.StartY]), nameOffset),
	                    angle,
	                    justify: [0, 0],
	                    size: textSize,
	                    thickness: 1,
	                    rejustifyOnRotate: true,
	                },
	            });
	        }
	        if (view.IsPinNumbersVisible && p.IsNumberVisible) {
	            drawings.push({
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinNumber].id],
	                drawing: {
	                    type: 'text',
	                    ...(0, ModelText_1.encodeStrippedText)({ raw: pinRef, dialects: [ModelText_1.TextDialect.Orcad] }),
	                    pos: (0, Transform_1.translatePoint)(pointTransform([(p.StartX + p.HotptX) / 2, (p.StartY + p.HotptY) / 2]), pinRefOffset),
	                    angle,
	                    justify: [0, 0],
	                    size: textSize,
	                    thickness: 1,
	                    rejustifyOnRotate: true,
	                },
	            });
	        }
	        return drawings;
	    }
	    scaleFont(font) {
	        return Math.abs(font.Height / 2);
	    }
	}
	OrcadDsnTransformComponentGenerator.OrCadDsnTransformComponentGenerator = OrCadDsnTransformComponentGenerator;
	
	return OrcadDsnTransformComponentGenerator;
}

var hasRequiredOrcadDsnTransformModuleGenerator;

function requireOrcadDsnTransformModuleGenerator () {
	if (hasRequiredOrcadDsnTransformModuleGenerator) return OrcadDsnTransformModuleGenerator;
	hasRequiredOrcadDsnTransformModuleGenerator = 1;
	Object.defineProperty(OrcadDsnTransformModuleGenerator, "__esModule", { value: true });
	OrcadDsnTransformModuleGenerator.OrCadDsnTransformModuleGenerator = void 0;
	OrcadDsnTransformModuleGenerator.pinPositionMapWithLiveFallback = pinPositionMapWithLiveFallback;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const alphaDesignatorToConstant_1 = requireAlphaDesignatorToConstant();
	const TransformModule_1 = requireTransformModule();
	const TransformMultiInstanceDisplayModule_1 = requireTransformMultiInstanceDisplayModule();
	const TransformMultiInstanceSchematic_1 = requireTransformMultiInstanceSchematic();
	const TransformModulePinConnection_1 = requireTransformModulePinConnection();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const TransformProjectModule_1 = requireTransformProjectModule();
	const TransformProject_1 = requireTransformProject();
	const TransformObject_1 = requireTransformObject();
	const orcadLayers_1 = requireOrcadLayers();
	const OrcadDsnTransformComponentGenerator_1 = requireOrcadDsnTransformComponentGenerator();
	const OrcadDsnPhysicalParts_1 = requireOrcadDsnPhysicalParts();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const orcadPinRefs_1 = requireOrcadPinRefs();
	const OrcadDsnSectionNumbering_1 = requireOrcadDsnSectionNumbering();
	const OrcadDsnSectionEmission_1 = requireOrcadDsnSectionEmission();
	const ModelText_1 = requireModelText();
	function pinPositionMapWithLiveFallback(cachePinPositionMap, portInsts) {
	    return cachePinPositionMap && cachePinPositionMap.size > 0 ?
	        cachePinPositionMap
	        : new Map(portInsts.map((portInst) => [
	            portInst.Position,
	            portInst.Name === '' ? String(portInst.Position + 1) : (portInst.Name ?? String(portInst.Position + 1)),
	        ]));
	}
	function symbolPinPositionsOfView(view) {
	    const map = new Map();
	    for (const pin of [...(view.PinScalar ?? []), ...(view.PinBus ?? [])]) {
	        map.set(pin.Position, { HotptX: pin.HotptX, HotptY: pin.HotptY });
	    }
	    return map;
	}
	class OrCadDsnTransformModuleGenerator {
	    constructor() {
	        this.recordedPinMismatchDiagnosticsEmitted = 0;
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	        this.physicalPartResultsEmitted = new WeakSet();
	        this.physicalPartResolver = new OrcadDsnPhysicalParts_1.OrcadDsnPhysicalPartResolver();
	        this.emptyDesignatorSelections = new Map();
	    }
	    static { this.RECORDED_PIN_MISMATCH_DIAGNOSTIC_LIMIT = 25; }
	    *generate(parent, _rootContext) {
	        const page = parent.hierarchyEntry.page;
	        const instance = parent.instance;
	        yield* this.reportPhysicalParts(parent, parent.hierarchyEntry.design);
	        for (const partInst of page.PartInst ?? []) {
	            if (this.isVerifiedReferenceOnlyHierarchyPart(parent, partInst)) {
	                if ((0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(partInst) && !this.expandsIntoSubSheet(parent, partInst)) {
	                    const keepsPhysicalPart = (0, OrcadDsnUtils_1.unresolvedBlockIsPhysicalPart)(this.expandsIntoSubSheet(parent, partInst), parent.hierarchyEntry.design, partInst);
	                    yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                        type: 'orcad-dsn-unresolved-sheet-reference',
	                        message: `[unresolved-sheet-reference] ${partInst.Reference}: schematic view "${partInst.ContentsViewName}" did not resolve to a sub-sheet; ${keepsPhysicalPart ?
	                            'the placement records pins and a PCB footprint, so its physical part is kept'
	                            : 'the placement stays a hierarchy carrier and contributes no physical part'}`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    });
	                    if (keepsPhysicalPart) {
	                        yield* this.generateModuleFromPartInst(parent, partInst, instance);
	                    }
	                }
	                continue;
	            }
	            yield* this.generateModuleFromPartInst(parent, partInst, instance);
	        }
	    }
	    isVerifiedReferenceOnlyHierarchyPart(parent, partInst) {
	        if ((0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(partInst)) {
	            return true;
	        }
	        if (partInst.ContentsLibName.length > 0) {
	            return false;
	        }
	        if (partInst.Reference !== '?') {
	            return false;
	        }
	        if (partInst.ContentsViewName.length === 0) {
	            return false;
	        }
	        const matchingSources = new Set(parent.context.allHierarchies
	            .filter((hierarchy) => hierarchy.sourceFile !== parent.hierarchyEntry.sourceFile &&
	            hierarchy.schematic.Name.toLowerCase() === partInst.ContentsViewName.toLowerCase())
	            .map((hierarchy) => hierarchy.sourceFile));
	        return matchingSources.size === 1;
	    }
	    expandsIntoSubSheet(parent, partInst) {
	        return parent.instance.children.some((child) => child.sheetSymbol === partInst);
	    }
	    *reportPhysicalParts(parent, design) {
	        if (this.physicalPartResultsEmitted.has(design)) {
	            return;
	        }
	        this.physicalPartResultsEmitted.add(design);
	        const physicalParts = this.physicalPartResolver.getIndexForDesign(design);
	        for (const part of physicalParts.parts.values()) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-physical-part-unified',
	                message: `Unified ${part.sections.length} library packages into component ${part.componentName} for reference ${part.reference}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	            });
	            const disagreement = (0, OrcadDsnPhysicalParts_1.findNumberingStyleDisagreement)(design.Cache, part.sections);
	            if (disagreement) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-physical-part-numbering-style-disagreement',
	                    message: `Sections of reference ${part.reference} disagree on section numbering style: ${disagreement.dissentingSectionName} is ${disagreement.dissentingStyle} but ${disagreement.chosenSectionName} is ${disagreement.chosenStyle}; every placement of ${part.reference} uses the ${disagreement.chosenStyle} style`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                });
	            }
	        }
	        for (const conflict of physicalParts.conflicts) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-physical-part-conflict',
	                message: `Reference ${conflict.reference} spans multiple packages but was not unified: ${conflict.message}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	    }
	    *generateModuleFromPartInst(parent, partInst, instance) {
	        const design = parent.hierarchyEntry.design;
	        const dbId = partInst.DbId.toString();
	        const moduleRef = `${instance.pathUniqueId}::${dbId}`;
	        const instOccurrence = this.findInstOccurrence(instance, dbId);
	        const bareReferenceDesignator = instOccurrence?.UserProperty?.find((p) => p.Name === 'Part Reference')?.Value ?? partInst.Reference;
	        const fullModuleName = instOccurrence?.UserProperty?.find((p) => p.Name === 'Reference')?.Value ?? partInst.Reference;
	        const occurrenceUniqueFullModuleName = fullModuleName === '?' ? `?@${moduleRef}` : fullModuleName;
	        const physicalPart = this.physicalPartResolver
	            .getIndexForDesign(design)
	            .parts.get(this.physicalPartResolver.getPhysicalPartReference(design, partInst));
	        const emptyDesignatorSelection = this.resolveEmptyDesignatorPhysicalPartSelection(design, instance, partInst, bareReferenceDesignator);
	        if (emptyDesignatorSelection) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: emptyDesignatorSelection.kind === 'inferred' ?
	                    'orcad-dsn-empty-designator-physical-part-inferred'
	                    : 'orcad-dsn-empty-designator-physical-part-declined',
	                message: emptyDesignatorSelection.message,
	                severity: emptyDesignatorSelection.kind === 'inferred' ? sigma_engine_shared_1.BoundaryLevel.Recommendation : sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        const referenceDesignator = this.resolvePlacementSectionName(design, partInst, bareReferenceDesignator, physicalPart, emptyDesignatorSelection);
	        const symbolResolution = this.resolveSymbolRef(design, partInst, emptyDesignatorSelection);
	        if (!symbolResolution) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-module-missing-symbol',
	                message: `Could not find symbol for ${partInst.LibName}::${partInst.PkgName}::${partInst.GraphicName} (${referenceDesignator})`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            });
	            return;
	        }
	        if (symbolResolution.usedFallback) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'symbol-lookup-fallback-physical-part-index',
	                message: `Found symbol for ${partInst.LibName}::${partInst.PkgName}::${partInst.GraphicName} (${referenceDesignator}) using backup physical part index`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        const boundingBox = symbolResolution.boundingBox;
	        const center = OrcadDsnUtils_1.OrcadDsnUtils.computeModuleCenter(partInst, boundingBox);
	        const metadata = {};
	        metadata['Value'] = partInst.PartValue;
	        for (const userProp of partInst.UserProperty ?? []) {
	            metadata[userProp.Name] = userProp.Value;
	        }
	        const component = new OrcadDsnTransformComponentGenerator_1.OrCadDsnTransformComponent(parent, {
	            libName: partInst.LibName,
	            packageName: partInst.PkgName,
	            libPartCellName: partInst.GraphicName,
	            viewName: 'NormalView',
	            viewSuffix: symbolResolution.viewSuffix,
	            physicalPartSuffix: symbolResolution.physicalPartSuffix,
	            packageIndex: symbolResolution.packageIndex,
	            sourceDesign: design,
	            physicalPartSections: physicalPart?.sections,
	        });
	        yield component;
	        const drawProperties = this.buildDrawProperties(parent, partInst, referenceDesignator);
	        const isMultiInstance = instance.totalSheetInstances > 1;
	        const moduleProps = {
	            ref: moduleRef,
	            footprintRef: symbolResolution.symbolRef,
	            center,
	            transform: this.computeModuleTransform(partInst),
	            name: referenceDesignator,
	            fullModuleName: occurrenceUniqueFullModuleName,
	            drawProperties: drawProperties.props,
	            metadata,
	            multiInstance: isMultiInstance ?
	                {
	                    instanceId: parent.designId,
	                    displayModuleRef: dbId,
	                }
	                : undefined,
	        };
	        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
	        const projectModuleIdentity = occurrenceUniqueFullModuleName ?? referenceDesignator;
	        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
	            name: projectModuleIdentity,
	            component,
	            value: metadata['Value']?.toString(),
	        });
	        yield projectModule;
	        const transformModule = new TransformModule_1.TransformModule(parent, moduleProps, component, projectModule);
	        yield transformModule;
	        if (isMultiInstance && instance.instanceIndex === 0) {
	            const multiInstanceParent = parent.parent;
	            if (multiInstanceParent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	                const displayDrawProperties = this.buildDrawProperties(parent, partInst, partInst.Reference);
	                const displayModuleProps = {
	                    ref: dbId,
	                    footprintRef: symbolResolution.symbolRef,
	                    center,
	                    transform: this.computeModuleTransform(partInst),
	                    name: partInst.Reference,
	                    fullModuleName: partInst.Reference,
	                    drawProperties: displayDrawProperties.props,
	                    metadata,
	                };
	                yield new TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule(multiInstanceParent, displayModuleProps, component);
	            }
	        }
	        yield* drawProperties.warnings;
	        yield* this.createPinConnections(parent, design, partInst, moduleRef, occurrenceUniqueFullModuleName ?? referenceDesignator, boundingBox, emptyDesignatorSelection);
	    }
	    findInstOccurrence(instance, refInstId) {
	        const occurrences = instance.instOccurence.InstOccurrence ?? [];
	        return occurrences.find((o) => o.RefInstId === refInstId);
	    }
	    resolveEmptyDesignatorPhysicalPartSelection(design, instance, partInst, reference) {
	        const placementKey = `${instance.pathUniqueId}::${partInst.DbId}`;
	        const cached = this.emptyDesignatorSelections.get(placementKey);
	        if (cached) {
	            return cached;
	        }
	        if (partInst.DeviceDesignator !== '') {
	            return undefined;
	        }
	        const resolution = design.Cache && OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(design.Cache, partInst);
	        const libPart = resolution?.pkg.LibPart.find((candidate) => !!candidate.CellName && OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, candidate.CellName));
	        const physicalParts = libPart?.PhysicalPart ?? [];
	        if (!resolution || physicalParts.length < 2) {
	            return undefined;
	        }
	        const schematic = instance.hierarchyEntry.schematic;
	        const schematicIndex = (design.Schematic ?? []).indexOf(schematic);
	        const pageOrdinalOffset = (design.Schematic ?? [])
	            .slice(0, Math.max(0, schematicIndex))
	            .reduce((total, candidate) => total + (candidate.Page?.length ?? 0), 0);
	        const placements = (schematic.Page ?? []).flatMap((page, pageOrdinal) => (page.PartInst ?? [])
	            .filter((candidate) => this.physicalPartResolver.getPhysicalPartReference(design, candidate) === reference &&
	            candidate.LibName === partInst.LibName &&
	            candidate.PkgName === partInst.PkgName &&
	            candidate.GraphicName === partInst.GraphicName)
	            .map((candidate) => ({
	            key: `${instance.pathUniqueId}::${candidate.DbId}`,
	            dbId: candidate.DbId,
	            pageOrdinal: pageOrdinalOffset + pageOrdinal,
	            deviceDesignator: candidate.DeviceDesignator,
	            portSurface: [...(candidate.PortInstScalar ?? []), ...(candidate.PortInstBus ?? [])]
	                .sort((left, right) => left.Position - right.Position || left.Name.localeCompare(right.Name))
	                .map((port) => `${port.Position}:${port.Name}`)
	                .join('|'),
	        })));
	        const explicitClaims = placements
	            .filter((candidate) => candidate.deviceDesignator !== '')
	            .map((candidate) => this.slottedSectionIndexFromDeviceDesignator(candidate.deviceDesignator, physicalParts.length));
	        if (explicitClaims.some((claim) => claim === undefined) || new Set(explicitClaims).size !== explicitClaims.length) {
	            return undefined;
	        }
	        const unclaimed = physicalParts.filter((_, index) => !explicitClaims.includes(index));
	        const selection = (0, OrcadDsnPhysicalParts_1.resolveEmptyDesignatorPhysicalPartBijection)(placements, unclaimed);
	        for (const [key, value] of selection) {
	            if (value.kind === 'inferred') {
	                const physicalPartIndex = physicalParts.indexOf(unclaimed[value.physicalPartIndex]);
	                this.emptyDesignatorSelections.set(key, { ...value, physicalPartIndex });
	            }
	            else {
	                this.emptyDesignatorSelections.set(key, value);
	            }
	        }
	        return this.emptyDesignatorSelections.get(placementKey);
	    }
	    resolvePlacementSectionName(design, partInst, bareReferenceDesignator, physicalPart, emptyDesignatorSelection) {
	        const cache = design.Cache;
	        if (!cache) {
	            return bareReferenceDesignator;
	        }
	        if (physicalPart && physicalPart.sections.length > 1) {
	            const sectionIndex = physicalPart.sections.findIndex((section) => section.libName === partInst.LibName && section.packageName === partInst.PkgName);
	            const firstSectionResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, physicalPart.sections[0].representativePartInst);
	            if (sectionIndex === -1 || !firstSectionResolution) {
	                return bareReferenceDesignator;
	            }
	            return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(firstSectionResolution.pkg.AlphabeticNumbering));
	        }
	        const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
	        if (!resolution) {
	            return bareReferenceDesignator;
	        }
	        const { pkg } = resolution;
	        const style = (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(pkg.AlphabeticNumbering);
	        const genuineSections = (0, OrcadDsnSectionEmission_1.enumerateGenuineSections)(pkg.LibPart, 'perLibPart');
	        if (genuineSections.length <= 1) {
	            return bareReferenceDesignator;
	        }
	        if (pkg.LibPart.length > 1) {
	            const matchedLibPart = pkg.LibPart.find((libPart) => !!libPart.CellName && OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName));
	            const sectionIndex = matchedLibPart ? genuineSections.findIndex((section) => section.libPart === matchedLibPart) : -1;
	            if (sectionIndex === -1) {
	                return bareReferenceDesignator;
	            }
	            return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, style);
	        }
	        const physicalParts = pkg.LibPart[0]?.PhysicalPart ?? [];
	        if (emptyDesignatorSelection?.kind === 'inferred') {
	            const matchedPhysicalPart = physicalParts[emptyDesignatorSelection.physicalPartIndex];
	            const sectionIndex = genuineSections.findIndex((section) => section.physicalPart === matchedPhysicalPart);
	            if (sectionIndex !== -1) {
	                return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, style);
	            }
	        }
	        const rawIndex = this.slottedSectionIndexFromDeviceDesignator(partInst.DeviceDesignator, physicalParts.length);
	        if (rawIndex !== undefined) {
	            const matchedPhysicalPart = physicalParts[rawIndex];
	            const sectionIndex = genuineSections.findIndex((section) => section.physicalPart === matchedPhysicalPart);
	            if (sectionIndex !== -1) {
	                return (0, OrcadDsnSectionNumbering_1.orcadDsnSectionPlacementName)(bareReferenceDesignator, sectionIndex, style);
	            }
	        }
	        return bareReferenceDesignator;
	    }
	    slottedSectionIndexFromDeviceDesignator(deviceDesignator, sectionCount) {
	        if ((0, alphaDesignatorToConstant_1.isAlphaDesignator)(deviceDesignator)) {
	            const sectionIndex = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(deviceDesignator) - 1;
	            return sectionIndex >= 0 && sectionIndex < sectionCount ? sectionIndex : undefined;
	        }
	        if (/^-?\d+$/.test(deviceDesignator)) {
	            const numericDesignator = parseInt(deviceDesignator, 10);
	            if (numericDesignator !== 0) {
	                const sectionIndex = Math.abs(numericDesignator) - 1;
	                return sectionIndex >= 0 && sectionIndex < sectionCount ? sectionIndex : undefined;
	            }
	        }
	        return undefined;
	    }
	    resolveSymbolRef(design, partInst, emptyDesignatorSelection) {
	        const cache = design.Cache;
	        if (!cache) {
	            return undefined;
	        }
	        const packageResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
	        if (!packageResolution) {
	            return undefined;
	        }
	        const { pkg, packageIndex } = packageResolution;
	        const inferredPhysicalPartIndex = emptyDesignatorSelection?.kind === 'inferred' ? emptyDesignatorSelection.physicalPartIndex : undefined;
	        for (const libPart of pkg.LibPart) {
	            for (const normalView of libPart.NormalView ?? []) {
	                const physicalParts = libPart.PhysicalPart ?? [];
	                for (const physicalPart of physicalParts) {
	                    const physIdx = physicalParts.indexOf(physicalPart);
	                    const physSuffix = physicalParts.length > 1 ? `-${physIdx + 1}` : '';
	                    const ref = `${libPart.CellName}${normalView.Suffix}${physSuffix}`;
	                    if (ref === `${partInst.GraphicName}${partInst.DeviceDesignator}`) {
	                        return {
	                            symbolRef: ref,
	                            boundingBox: normalView.BoundingBox,
	                            viewSuffix: normalView.Suffix,
	                            physicalPartSuffix: physSuffix,
	                            packageIndex,
	                            usedFallback: false,
	                        };
	                    }
	                }
	            }
	        }
	        if (inferredPhysicalPartIndex !== undefined) {
	            for (const libPart of pkg.LibPart.filter((candidate) => !!candidate.CellName && OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, candidate.CellName))) {
	                for (const normalView of libPart.NormalView ?? []) {
	                    if ((libPart.PhysicalPart?.length ?? 0) > inferredPhysicalPartIndex) {
	                        const ref = `${libPart.CellName}${normalView.Suffix}-${inferredPhysicalPartIndex + 1}`;
	                        return {
	                            symbolRef: ref,
	                            boundingBox: normalView.BoundingBox,
	                            viewSuffix: normalView.Suffix,
	                            physicalPartSuffix: `-${inferredPhysicalPartIndex + 1}`,
	                            packageIndex,
	                            usedFallback: false,
	                        };
	                    }
	                }
	            }
	        }
	        for (const libPart of pkg.LibPart) {
	            for (const normalView of libPart.NormalView ?? []) {
	                const ref = `${libPart.CellName}${normalView.Suffix}`;
	                if (ref === partInst.GraphicName && (libPart.PhysicalPart?.length ?? 0) <= 1) {
	                    return {
	                        symbolRef: ref,
	                        boundingBox: normalView.BoundingBox,
	                        viewSuffix: normalView.Suffix,
	                        physicalPartSuffix: '',
	                        packageIndex,
	                        usedFallback: false,
	                    };
	                }
	            }
	        }
	        if ((0, alphaDesignatorToConstant_1.isAlphaDesignator)(partInst.DeviceDesignator)) {
	            const physicalPartIndex = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(partInst.DeviceDesignator);
	            for (const libPart of pkg.LibPart) {
	                for (const normalView of libPart.NormalView ?? []) {
	                    if ((libPart.PhysicalPart?.length ?? 0) > 1) {
	                        const ref = `${libPart.CellName}${normalView.Suffix}-${physicalPartIndex}`;
	                        return {
	                            symbolRef: ref,
	                            boundingBox: normalView.BoundingBox,
	                            viewSuffix: normalView.Suffix,
	                            physicalPartSuffix: `-${physicalPartIndex}`,
	                            packageIndex,
	                            usedFallback: true,
	                        };
	                    }
	                }
	            }
	        }
	        const numericDesignator = parseInt(partInst.DeviceDesignator, 10);
	        if (!isNaN(numericDesignator) && numericDesignator !== 0) {
	            const physicalPartIndex = Math.abs(numericDesignator);
	            for (const libPart of pkg.LibPart) {
	                for (const normalView of libPart.NormalView ?? []) {
	                    if ((libPart.PhysicalPart?.length ?? 0) > 1) {
	                        const ref = `${libPart.CellName}${normalView.Suffix}-${physicalPartIndex}`;
	                        return {
	                            symbolRef: ref,
	                            boundingBox: normalView.BoundingBox,
	                            viewSuffix: normalView.Suffix,
	                            physicalPartSuffix: `-${physicalPartIndex}`,
	                            packageIndex,
	                            usedFallback: true,
	                        };
	                    }
	                }
	            }
	        }
	        for (const libPart of pkg.LibPart) {
	            for (const normalView of libPart.NormalView ?? []) {
	                const ref = `${libPart.CellName}${normalView.Suffix}`;
	                if (ref === partInst.GraphicName && (libPart.PhysicalPart?.length ?? 0) > 1) {
	                    return {
	                        symbolRef: `${ref}-1`,
	                        boundingBox: normalView.BoundingBox,
	                        viewSuffix: normalView.Suffix,
	                        physicalPartSuffix: '-1',
	                        packageIndex,
	                        usedFallback: true,
	                    };
	                }
	            }
	        }
	        return undefined;
	    }
	    computeModuleTransform(inst) {
	        const { angle, mirrored } = OrcadDsnUtils_1.OrcadDsnUtils.orcadRotationToCadstrom(inst.Rotation, inst.Mirror);
	        return {
	            pos: [0, 0],
	            angle,
	            mirrored,
	        };
	    }
	    buildDrawProperties(parent, partInst, referenceDesignator) {
	        const drawProperties = {};
	        const warnings = [];
	        const namedValues = {
	            'Part Reference': referenceDesignator,
	            Value: partInst.PartValue,
	        };
	        for (const userProp of partInst.UserProperty ?? []) {
	            namedValues[userProp.Name] = userProp.Value;
	        }
	        for (const prop of partInst.DisplayProperty ?? []) {
	            let layerType = sigma_engine_shared_1.SchLayerType.Label;
	            switch (prop.Name) {
	                case 'Name':
	                    layerType = sigma_engine_shared_1.SchLayerType.NameLabel;
	                    break;
	                case 'Value':
	                    layerType = sigma_engine_shared_1.SchLayerType.ValueLabel;
	                    break;
	            }
	            const namedValue = namedValues[prop.Name];
	            if (namedValue === undefined) {
	                warnings.push(new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-module-missing-draw-property-value',
	                    message: `Could not find value for defined display property ${prop.Name} on ${partInst.Reference}`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                }));
	                continue;
	            }
	            drawProperties[prop.Name] = {
	                ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
	                pos: [prop.LocX + partInst.LocX, prop.LocY + partInst.LocY],
	                layer: orcadLayers_1.OrCadSchLayerMap[layerType].id,
	                angle: prop.Rotation * 90,
	                justify: [-1, -1],
	                size: this.scaleFont(prop.Font),
	                thickness: 1,
	                hidden: false,
	                ref_designator: prop.Name === 'Part Reference',
	                mirror: [false, false],
	            };
	        }
	        return { props: drawProperties, warnings };
	    }
	    *createPinConnections(parent, design, partInst, moduleRef, moduleName, boundingBox, emptyDesignatorSelection) {
	        const portInsts = [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])];
	        const cachePinPositionMap = this.buildPinPositionMapForPartInst(design, partInst, emptyDesignatorSelection);
	        const pinPositionMap = pinPositionMapWithLiveFallback(cachePinPositionMap, portInsts);
	        const pinRefByPosition = (0, orcadPinRefs_1.buildPinRefByPosition)(pinPositionMap);
	        const symbolPinResolution = this.buildSymbolPinPositions(design, partInst);
	        const symbolPinPositions = symbolPinResolution?.positions;
	        if (symbolPinResolution !== undefined && !symbolPinResolution.placedViewResolved) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-placed-view-unresolved',
	                message: `[placed-view-unresolved] ${partInst.Reference}: graphic name "${partInst.GraphicName}" matches none of the cached views, so recorded pin positions stand unchallenged`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        const permutedRecordedPins = symbolPinResolution?.placedViewResolved === true ?
	            this.findPermutedRecordedPins(parent.hierarchyEntry.page, partInst, portInsts, symbolPinPositions, boundingBox)
	            : new Set();
	        if (permutedRecordedPins.size > 0 &&
	            this.recordedPinMismatchDiagnosticsEmitted <
	                OrCadDsnTransformModuleGenerator.RECORDED_PIN_MISMATCH_DIAGNOSTIC_LIMIT) {
	            this.recordedPinMismatchDiagnosticsEmitted++;
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-recorded-pin-provenance-mismatch',
	                message: `[recorded-pin-provenance-mismatch] ${partInst.Reference}: ${permutedRecordedPins.size} recorded pin positions coincide with different wired pins on the same placement; using symbol geometry for those pins`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        for (const portInst of portInsts) {
	            if (portInst.RequiresRecordedPosition === true &&
	                (portInst.RecordedX === undefined || portInst.RecordedY === undefined)) {
	                continue;
	            }
	            const pinNumber = pinPositionMap.get(portInst.Position);
	            if (pinNumber === undefined) {
	                continue;
	            }
	            const effectivePinRef = pinRefByPosition.get(portInst.Position) ?? pinNumber;
	            let pinPosition;
	            const recordedPinPosition = permutedRecordedPins.has(portInst.Position) ? {} : this.validRecordedPinPosition(parent, partInst, portInst);
	            if (recordedPinPosition.mismatch &&
	                this.recordedPinMismatchDiagnosticsEmitted <
	                    OrCadDsnTransformModuleGenerator.RECORDED_PIN_MISMATCH_DIAGNOSTIC_LIMIT) {
	                this.recordedPinMismatchDiagnosticsEmitted++;
	                const { wireDbId, position } = recordedPinPosition.mismatch;
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-recorded-pin-mismatch',
	                    message: `[recorded-pin-mismatch] ${partInst.Reference} pin ${portInst.Name || portInst.Position}: recorded (${position.join(', ')}) does not lie on page wire ${wireDbId}`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            if (portInst.RequiresRecordedPosition === true && !recordedPinPosition.position) {
	                continue;
	            }
	            if (recordedPinPosition.position) {
	                pinPosition = recordedPinPosition.position;
	            }
	            else {
	                const symbolPin = symbolPinPositions?.get(portInst.Position);
	                if (symbolPin) {
	                    pinPosition = OrcadDsnUtils_1.OrcadDsnUtils.transformPinToSchematicCoords(symbolPin, partInst, boundingBox);
	                }
	                else {
	                    pinPosition = [portInst.HotptX + partInst.LocX, portInst.HotptY + partInst.LocY];
	                }
	            }
	            const isPowerPin = portInst.Type === 7 && !!portInst.Name;
	            const appliesNetName = isPowerPin ? { label: portInst.Name, labelType: 'power-pin' } : undefined;
	            const skipPositionalCombobulation = portInst.Visible === false;
	            yield new TransformModulePinConnection_1.TransformModulePinConnection(parent, {
	                position: pinPosition,
	                connection: {
	                    moduleRef,
	                    moduleName,
	                    pinRef: effectivePinRef,
	                    pinNumber,
	                    pinPosition: portInst.Position,
	                    appliesNetName,
	                    skipPositionalCombobulation,
	                },
	            });
	        }
	    }
	    findPermutedRecordedPins(page, partInst, portInsts, symbolPinPositions, boundingBox) {
	        const geometryByPoint = new Map();
	        const geometryByPosition = new Map();
	        for (const portInst of portInsts) {
	            if (!symbolPinPositions?.has(portInst.Position)) {
	                continue;
	            }
	            const geometry = this.geometryPinPosition(partInst, portInst, symbolPinPositions, boundingBox);
	            geometryByPosition.set(portInst.Position, geometry);
	            const key = geometry.join(',');
	            const positions = geometryByPoint.get(key) ?? new Set();
	            positions.add(portInst.Position);
	            geometryByPoint.set(key, positions);
	        }
	        const collisions = new Set();
	        for (const portInst of portInsts) {
	            if (portInst.RecordedWireDbId === undefined ||
	                portInst.RecordedX === undefined ||
	                portInst.RecordedY === undefined) {
	                continue;
	            }
	            const recorded = [portInst.RecordedX, portInst.RecordedY];
	            const ownGeometry = geometryByPosition.get(portInst.Position);
	            if (!ownGeometry || (recorded[0] === ownGeometry[0] && recorded[1] === ownGeometry[1])) {
	                continue;
	            }
	            const positionsAtRecordedPoint = geometryByPoint.get(recorded.join(','));
	            if (positionsAtRecordedPoint &&
	                Array.from(positionsAtRecordedPoint).some((position) => position !== portInst.Position)) {
	                collisions.add(portInst.Position);
	            }
	        }
	        if (collisions.size < 2) {
	            return new Set();
	        }
	        return new Set(Array.from(collisions).filter((position) => {
	            const geometry = geometryByPosition.get(position);
	            return geometry !== undefined && this.pageContainsPoint(page, geometry);
	        }));
	    }
	    geometryPinPosition(partInst, portInst, symbolPinPositions, boundingBox) {
	        const symbolPin = symbolPinPositions?.get(portInst.Position);
	        return symbolPin ?
	            OrcadDsnUtils_1.OrcadDsnUtils.transformPinToSchematicCoords(symbolPin, partInst, boundingBox)
	            : [portInst.HotptX + partInst.LocX, portInst.HotptY + partInst.LocY];
	    }
	    validRecordedPinPosition(parent, partInst, portInst) {
	        if (portInst.RecordedX === undefined || portInst.RecordedY === undefined) {
	            return {};
	        }
	        const recordedPosition = [portInst.RecordedX, portInst.RecordedY];
	        if (portInst.RecordedWireDbId !== undefined) {
	            const wire = this.findPageWire(parent.hierarchyEntry.page, portInst.RecordedWireDbId);
	            if (wire && this.wireContainsPoint(wire, recordedPosition)) {
	                return { position: recordedPosition };
	            }
	            return { mismatch: { wireDbId: portInst.RecordedWireDbId, position: recordedPosition } };
	        }
	        const placementIsNearOrigin = Math.abs(partInst.LocX) <= 1 && Math.abs(partInst.LocY) <= 1;
	        return {
	            position: recordedPosition[0] === 0 && recordedPosition[1] === 0 && !placementIsNearOrigin ? undefined : recordedPosition,
	        };
	    }
	    findPageWire(page, dbId) {
	        for (const net of page.NetScalar ?? []) {
	            const wire = net.WireScalars?.find((candidate) => candidate.DbId === dbId);
	            if (wire) {
	                return wire;
	            }
	        }
	        for (const net of page.NetBus ?? []) {
	            const wire = net.WireBus?.find((candidate) => candidate.DbId === dbId);
	            if (wire) {
	                return wire;
	            }
	            for (const scalarNet of net.NetScalar ?? []) {
	                const scalarWire = scalarNet.WireScalars?.find((candidate) => candidate.DbId === dbId);
	                if (scalarWire) {
	                    return scalarWire;
	                }
	            }
	        }
	        return undefined;
	    }
	    wireContainsPoint(wire, [x, y]) {
	        const crossProduct = (x - wire.StartX) * (wire.EndY - wire.StartY) - (y - wire.StartY) * (wire.EndX - wire.StartX);
	        return (crossProduct === 0 &&
	            x >= Math.min(wire.StartX, wire.EndX) &&
	            x <= Math.max(wire.StartX, wire.EndX) &&
	            y >= Math.min(wire.StartY, wire.EndY) &&
	            y <= Math.max(wire.StartY, wire.EndY));
	    }
	    pageContainsPoint(page, point) {
	        for (const net of page.NetScalar ?? []) {
	            if (net.WireScalars?.some((wire) => this.wireContainsPoint(wire, point))) {
	                return true;
	            }
	        }
	        for (const bus of page.NetBus ?? []) {
	            if (bus.WireBus?.some((wire) => this.wireContainsPoint(wire, point))) {
	                return true;
	            }
	            for (const net of bus.NetScalar ?? []) {
	                if (net.WireScalars?.some((wire) => this.wireContainsPoint(wire, point))) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    buildPinPositionMapForPartInst(design, partInst, emptyDesignatorSelection) {
	        const cache = design.Cache;
	        if (!cache) {
	            return undefined;
	        }
	        const packageResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
	        if (!packageResolution) {
	            return undefined;
	        }
	        const { pkg } = packageResolution;
	        for (const libPart of pkg.LibPart) {
	            if (!libPart.CellName || !OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
	                continue;
	            }
	            let physicalPart;
	            if (emptyDesignatorSelection?.kind === 'inferred') {
	                physicalPart = libPart.PhysicalPart?.[emptyDesignatorSelection.physicalPartIndex];
	            }
	            else if ((0, alphaDesignatorToConstant_1.isAlphaDesignator)(partInst.DeviceDesignator) && (libPart.PhysicalPart?.length ?? 0) > 1) {
	                const idx = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(partInst.DeviceDesignator) - 1;
	                physicalPart = libPart.PhysicalPart?.[idx];
	            }
	            else {
	                const numericIdx = /^-?\d+$/.test(partInst.DeviceDesignator) ? parseInt(partInst.DeviceDesignator, 10) : NaN;
	                if (!isNaN(numericIdx) && numericIdx !== 0 && (libPart.PhysicalPart?.length ?? 0) > 1) {
	                    physicalPart = libPart.PhysicalPart?.[Math.abs(numericIdx) - 1];
	                }
	                else {
	                    physicalPart = libPart.PhysicalPart?.[0];
	                }
	            }
	            if (!physicalPart) {
	                continue;
	            }
	            return (0, OrcadDsnSectionEmission_1.buildPinPositionMap)(physicalPart);
	        }
	        return undefined;
	    }
	    buildSymbolPinPositions(design, partInst) {
	        const cache = design.Cache;
	        if (!cache) {
	            return undefined;
	        }
	        const packageResolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
	        if (!packageResolution) {
	            return undefined;
	        }
	        const { pkg } = packageResolution;
	        const placedViewSuffix = (0, OrcadDsnUtils_1.placedViewSuffixOf)(partInst.GraphicName);
	        let fallback;
	        for (const libPart of pkg.LibPart) {
	            if (libPart.CellName === undefined ||
	                !OrcadDsnUtils_1.OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
	                continue;
	            }
	            for (const view of [...(libPart.NormalView ?? []), ...(libPart.ConvertView ?? [])]) {
	                const map = symbolPinPositionsOfView(view);
	                if (view.Suffix === placedViewSuffix) {
	                    return { positions: map, placedViewResolved: true };
	                }
	                fallback ??= map;
	            }
	        }
	        if (fallback === undefined) {
	            return undefined;
	        }
	        return { positions: fallback, placedViewResolved: placedViewSuffix === undefined };
	    }
	    scaleFont(font) {
	        return Math.abs(font.Height / 2);
	    }
	}
	OrcadDsnTransformModuleGenerator.OrCadDsnTransformModuleGenerator = OrCadDsnTransformModuleGenerator;
	
	return OrcadDsnTransformModuleGenerator;
}

var OrcadDsnTransformNetWireGenerator = {};

var OrcadDsnSymbolUtils = {};

var hasRequiredOrcadDsnSymbolUtils;

function requireOrcadDsnSymbolUtils () {
	if (hasRequiredOrcadDsnSymbolUtils) return OrcadDsnSymbolUtils;
	hasRequiredOrcadDsnSymbolUtils = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OrCadCrossReferenceDbIdMetadataKey = exports.OrCadCrossReferenceKindMetadataKey = void 0;
		exports.scaleFont = scaleFont;
		exports.findPortSymbol = findPortSymbol;
		exports.resolveSymbolVariant = resolveSymbolVariant;
		exports.computePortPinPosition = computePortPinPosition;
		exports.computeSymbolPinPosition = computeSymbolPinPosition;
		exports.generateSymbolGroupDrawing = generateSymbolGroupDrawing;
		const sigma_engine_shared_1 = requireDist$1();
		const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
		const TransformImageExtraction_1 = requireTransformImageExtraction();
		const orcadLayers_1 = requireOrcadLayers();
		const sigma_engine_shared_2 = requireDist$1();
		const orcadShapeConverters_1 = requireOrcadShapeConverters();
		const ModelText_1 = requireModelText();
		const { translatePoint, transformDrawing } = sigma_engine_shared_1.Utils.Transform;
		function scaleFont(font) {
		    return Math.abs(font.Height / 2);
		}
		function findPortSymbol(design, port) {
		    const symbols = design.Cache?.PortSymbol;
		    const symbolName = port.SymbolName ?? port.Name;
		    return resolveSymbolVariant(symbols, symbolName, port.LibName).symbol;
		}
		function canonicalSourceLibraryName(sourceLibName) {
		    const normalized = (sourceLibName ?? '').trim().replace(/\\/g, '/');
		    if (!normalized) {
		        return '';
		    }
		    const prefix = normalized.startsWith('//') ? '//'
		        : normalized.startsWith('/') ? '/'
		            : '';
		    const segments = [];
		    for (const segment of normalized.split('/')) {
		        if (!segment || segment === '.') {
		            continue;
		        }
		        if (segment === '..') {
		            if (segments.length > 0 && segments[segments.length - 1] !== '..') {
		                segments.pop();
		            }
		            else if (!prefix) {
		                segments.push(segment);
		            }
		            continue;
		        }
		        segments.push(segment);
		    }
		    return `${prefix}${segments.join('/')}`.toLowerCase();
		}
		function resolveSymbolVariant(symbols, symbolName, sourceLibName) {
		    const candidates = symbols?.filter((symbol) => symbol.Name === symbolName) ?? [];
		    const canonicalSourceLibName = canonicalSourceLibraryName(sourceLibName);
		    const exact = candidates.filter((symbol) => canonicalSourceLibraryName(symbol.SourceLibName) === canonicalSourceLibName);
		    if (exact.length === 1) {
		        return { symbol: exact[0], ambiguous: false };
		    }
		    return { symbol: undefined, ambiguous: (exact.length > 0 ? exact.length : candidates.length) > 1 };
		}
		function computePortPinPosition(design, port) {
		    const portSymbolDef = findPortSymbol(design, port);
		    const pin = portSymbolDef?.PinScalar;
		    if (!portSymbolDef || !pin) {
		        return { position: [port.LocX, port.LocY], pinRecorded: false };
		    }
		    return {
		        position: computeSymbolPinPosition([pin.HotptX, pin.HotptY], portSymbolDef.BoundingBox, port.LocX, port.LocY, port.Rotation ?? 0, port.Mirror ?? 0),
		        pinRecorded: true,
		    };
		}
		function computeSymbolPinPosition(pinLocal, boundingBox, locX, locY, rotation, mirror) {
		    const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
		    let centered = [pinLocal[0] - bboxCenter[0], pinLocal[1] - bboxCenter[1]];
		    if (mirror === 1) {
		        centered = [-centered[0], centered[1]];
		    }
		    const rot = rotation % 4;
		    if (rot === 1) {
		        centered = [centered[1], -centered[0]];
		    }
		    else if (rot === 2) {
		        centered = [-centered[0], -centered[1]];
		    }
		    else if (rot === 3) {
		        centered = [-centered[1], centered[0]];
		    }
		    const pointTransform = (p) => translatePoint(p, [bboxCenter[0], bboxCenter[1]]);
		    const oneWeirdTrick = (p) => translatePoint(translatePoint(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
		    let moduleCenter = pointTransform([locX, locY]);
		    if (rot % 2 === 1) {
		        moduleCenter = oneWeirdTrick(moduleCenter);
		    }
		    return [moduleCenter[0] + centered[0], moduleCenter[1] + centered[1]];
		}
		exports.OrCadCrossReferenceKindMetadataKey = 'orcadCrossReferenceKind';
		exports.OrCadCrossReferenceDbIdMetadataKey = 'orcadCrossReferenceDbId';
		function generateSymbolGroupDrawing(options) {
		    const { parent, symbolDef, boundingBox, locX, locY, rotation, mirror, name, displayProps, userProps, netRefObject, crossReferenceSource, } = options;
		    const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
		    const pointTransform = (p) => translatePoint(p, [-bboxCenter[0], -bboxCenter[1]]);
		    const symbolDrawings = orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(symbolDef);
		    const bitmapResult = orcadShapeConverters_1.OrCadShapeConvert.elementBitmaps({ pointTransform })(symbolDef);
		    const imageExtractions = bitmapResult.imageExtractions.map((ie) => new TransformImageExtraction_1.TransformImageExtraction(parent, ie.ref, ie.name, ie.data, ie.mimeType));
		    const offsetTransform = (p) => translatePoint(p, [bboxCenter[0], bboxCenter[1]]);
		    const oneWeirdTrick = (p) => translatePoint(translatePoint(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
		    let symbolCenterOffset = offsetTransform([0, 0]);
		    if (rotation % 2 === 1) {
		        symbolCenterOffset = oneWeirdTrick(symbolCenterOffset);
		    }
		    const mirrored = mirror === 1 ? [rotation === 1 || rotation === 3, rotation === 2 || rotation === 0] : [false, false];
		    const drawingRotation = rotation === 1 || rotation === 3 ? rotation + 2 : rotation;
		    const transformedDrawings = symbolDrawings.map((d) => transformDrawing(transformDrawing(d, { mirrored, angle: drawingRotation * 90, pos: [0, 0] }), {
		        mirrored: [false, false],
		        angle: 0,
		        pos: symbolCenterOffset,
		    }));
		    const namedValues = { Name: name };
		    for (const prop of userProps ?? []) {
		        namedValues[prop.Name] = prop.Value;
		    }
		    const textDrawings = [];
		    for (const displayProp of displayProps ?? []) {
		        const namedValue = namedValues[displayProp.Name];
		        if (namedValue === undefined) {
		            continue;
		        }
		        const fontSize = scaleFont(displayProp.Font);
		        textDrawings.push({
		            type: 'text',
		            ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
		            pos: translatePoint([displayProp.LocX, displayProp.LocY], [0, fontSize]),
		            angle: displayProp.Rotation * 90,
		            justify: [-1, -1],
		            size: fontSize,
		            thickness: 1,
		        });
		    }
		    const transformedBitmapDrawings = bitmapResult.drawings.map((d) => transformDrawing(transformDrawing(d, { mirrored, angle: drawingRotation * 90, pos: [0, 0] }), {
		        mirrored: [false, false],
		        angle: 0,
		        pos: symbolCenterOffset,
		    }));
		    const drawing = new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
		        layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_2.SchLayerType.Symbol].id],
		        drawing: {
		            type: 'group',
		            pos: [locX, locY],
		            angle: 0,
		            drawings: [...textDrawings, ...transformedDrawings, ...transformedBitmapDrawings],
		            metadata: crossReferenceSource ?
		                {
		                    [exports.OrCadCrossReferenceKindMetadataKey]: crossReferenceSource.kind,
		                    [exports.OrCadCrossReferenceDbIdMetadataKey]: crossReferenceSource.dbId,
		                }
		                : {},
		        },
		    }, netRefObject);
		    return { drawing, imageExtractions };
		}
		
	} (OrcadDsnSymbolUtils));
	return OrcadDsnSymbolUtils;
}

var hasRequiredOrcadDsnTransformNetWireGenerator;

function requireOrcadDsnTransformNetWireGenerator () {
	if (hasRequiredOrcadDsnTransformNetWireGenerator) return OrcadDsnTransformNetWireGenerator;
	hasRequiredOrcadDsnTransformNetWireGenerator = 1;
	Object.defineProperty(OrcadDsnTransformNetWireGenerator, "__esModule", { value: true });
	OrcadDsnTransformNetWireGenerator.OrcadDsnTransformNetWireGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformNetWire_1 = requireTransformNetWire();
	const TransformNetLabel_1 = requireTransformNetLabel();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	const orcadLayers_1 = requireOrcadLayers();
	const OrcadDsnBusUtils_1 = requireOrcadDsnBusUtils();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const ModelText_1 = requireModelText();
	class OrcadDsnTransformNetWireGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	    }
	    *generate(parent, _rootContext) {
	        const page = parent.hierarchyEntry.page;
	        const signalEndpointToDimension = this.buildBusEntryDimensionMap(page);
	        for (const netScalar of page.NetScalar ?? []) {
	            yield* this.generateFromNetScalar(parent, netScalar);
	        }
	        for (const netBus of page.NetBus ?? []) {
	            yield* this.generateFromNetBus(parent, netBus);
	        }
	        for (const busEntry of page.BusEntry ?? []) {
	            let signalDimension = signalEndpointToDimension.get(`${busEntry.EndX},${busEntry.EndY}`);
	            if (!signalDimension) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-transformer',
	                    message: `Could not find signal dimension for bus entry at ${busEntry.EndX},${busEntry.EndY} in ${parent.sourceFile} (${parent.designId})`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	                continue;
	            }
	            let busPosition = [busEntry.BusX, busEntry.BusY];
	            let wirePosition = [busEntry.EndX, busEntry.EndY];
	            if (!signalDimension) {
	                signalDimension = signalEndpointToDimension.get(`${busEntry.BusX},${busEntry.BusY}`);
	                if (signalDimension) {
	                    busPosition = [busEntry.EndX, busEntry.EndY];
	                    wirePosition = [busEntry.BusX, busEntry.BusY];
	                }
	            }
	            yield new TransformNetWire_1.TransformNetWire(parent, busPosition, wirePosition, {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id],
	                drawing: {
	                    type: 'segment',
	                    start: busPosition,
	                    end: wirePosition,
	                    width: 0.3,
	                },
	            }, typeof signalDimension === 'object' ? signalDimension.name : undefined, undefined, typeof signalDimension === 'object' ?
	                {
	                    'bus-entry': {
	                        busName: signalDimension.name,
	                        memberName: signalDimension.name,
	                        memberIndex: signalDimension.index,
	                    },
	                }
	                : undefined);
	            if (typeof signalDimension === 'object') {
	                yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: busPosition,
	                    originalLabel: signalDimension.name,
	                    labelType: 'virtual-bus-member',
	                    dimensions: [signalDimension.name],
	                });
	            }
	        }
	    }
	    buildBusEntryDimensionMap(page) {
	        const map = new Map();
	        for (const netBus of page.NetBus ?? []) {
	            const memberDims = (0, OrcadDsnBusUtils_1.getNetBusMemberDimensionsByName)(netBus, (0, OrcadDsnBusUtils_1.inferNetBusName)(netBus));
	            const dimByName = new Map(memberDims.map((m) => [m.name, m.index]));
	            for (const netScalar of netBus.NetScalar ?? []) {
	                if (!netScalar.Name) {
	                    continue;
	                }
	                const dimension = dimByName.get(netScalar.Name);
	                if (!dimension) {
	                    continue;
	                }
	                for (const wire of netScalar.WireScalars ?? []) {
	                    map.set(`${wire.StartX},${wire.StartY}`, { name: netScalar.Name, index: dimension });
	                    map.set(`${wire.EndX},${wire.EndY}`, { name: netScalar.Name, index: dimension });
	                }
	            }
	            for (const wireBus of netBus.WireBus ?? []) {
	                map.set(`${wireBus.StartX},${wireBus.StartY}`, wireBus.Name || netBus.Name);
	                map.set(`${wireBus.EndX},${wireBus.EndY}`, wireBus.Name || netBus.Name);
	            }
	        }
	        return map;
	    }
	    *generateFromNetScalar(parent, netScalar) {
	        const wires = netScalar.WireScalars ?? [];
	        const netScalarId = wires.length >= 2 ? this.netScalarIdentityFor(wires) : undefined;
	        for (const wire of wires) {
	            yield* this.generateFromWireScalar(parent, wire, netScalarId);
	        }
	    }
	    netScalarIdentityFor(wires) {
	        let minDbId;
	        for (const wire of wires) {
	            if (wire.DbId === undefined) {
	                continue;
	            }
	            if (minDbId === undefined || wire.DbId < minDbId) {
	                minDbId = wire.DbId;
	            }
	        }
	        return minDbId === undefined ? undefined : `NS${String(minDbId).padStart(5, '0')}`;
	    }
	    *generateFromNetBus(parent, netBus) {
	        const busName = (0, OrcadDsnBusUtils_1.inferNetBusName)(netBus) ?? netBus.Name;
	        const busMembers = (0, OrcadDsnBusUtils_1.getNetBusMemberDimensionsByName)(netBus, busName);
	        for (const wire of netBus.WireBus ?? []) {
	            const busWidth = Math.max(wire.LineWidth || 0, 1);
	            yield new TransformNetWire_1.TransformNetWire(parent, [wire.StartX, wire.StartY], [wire.EndX, wire.EndY], {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Bus].id],
	                drawing: {
	                    type: 'segment',
	                    start: [wire.StartX, wire.StartY],
	                    end: [wire.EndX, wire.EndY],
	                    width: busWidth,
	                },
	            }, 'bus', 'bus', {
	                bus: {
	                    name: busName,
	                    members: busMembers,
	                },
	            });
	            for (const { name, index } of busMembers) {
	                const dimensionName = (0, OrcadDsnBusUtils_1.busDimensionName)({
	                    index,
	                    name,
	                });
	                const strategyMetadata = {
	                    'bus-member': {
	                        busName: netBus.Name,
	                        memberName: name,
	                        memberIndex: index,
	                    },
	                };
	                yield new TransformNetWire_1.TransformNetWire(parent, [wire.StartX, wire.StartY], [wire.EndX, wire.EndY], {
	                    layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Bus].id],
	                    drawing: {
	                        type: 'segment',
	                        start: [wire.StartX, wire.StartY],
	                        end: [wire.EndX, wire.EndY],
	                        width: busWidth,
	                    },
	                }, dimensionName, dimensionName, strategyMetadata);
	                yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: [wire.StartX, wire.StartY],
	                    originalLabel: name,
	                    labelType: 'virtual-bus-member',
	                    dimensions: [dimensionName],
	                    strategyMetadata: strategyMetadata,
	                });
	            }
	            for (const alias of wire.Alias ?? []) {
	                if (!alias.Name) {
	                    continue;
	                }
	                const labelPos = [alias.LocX ?? wire.StartX, alias.LocY ?? wire.StartY];
	                yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: labelPos,
	                    originalLabel: alias.Name,
	                    labelType: 'alias',
	                    dimensions: ['bus'],
	                });
	            }
	        }
	        for (const netScalar of netBus.NetScalar ?? []) {
	            yield* this.generateFromNetScalar(parent, netScalar);
	        }
	    }
	    *generateFromWireScalar(parent, wire, netScalarId) {
	        const from = [wire.StartX, wire.StartY];
	        const to = [wire.EndX, wire.EndY];
	        const wireWidth = Math.max((wire.LineWidth || 0) * 0.1, 1);
	        const systemId = wire.DbId ? `N${String(wire.DbId).padStart(5, '0')}` : undefined;
	        const strategyMetadata = { 'source-wire-db-id': wire.DbId };
	        if (systemId !== undefined) {
	            strategyMetadata['system-id'] = systemId;
	        }
	        if (wire.UserProperty?.length) {
	            strategyMetadata['net-properties'] = wire.UserProperty.map((property) => ({
	                name: property.Name,
	                value: property.Value,
	            }));
	        }
	        if (netScalarId !== undefined) {
	            strategyMetadata['net-scalar-id'] = netScalarId;
	        }
	        const wireObj = new TransformNetWire_1.TransformNetWire(parent, from, to, {
	            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id],
	            drawing: {
	                type: 'segment',
	                start: from,
	                end: to,
	                width: wireWidth,
	            },
	        }, undefined, undefined, Object.keys(strategyMetadata).length > 0 ? strategyMetadata : undefined);
	        yield wireObj;
	        for (const junction of wire.Junction ?? []) {
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Junction].id],
	                drawing: {
	                    type: 'junction',
	                    position: [junction.LocX, junction.LocY],
	                    radiusOverride: 1.25,
	                },
	            }, wireObj);
	        }
	        for (const alias of wire.Alias ?? []) {
	            if (!alias.Name) {
	                continue;
	            }
	            const netLabel = new TransformNetLabel_1.TransformNetLabel(parent, {
	                position: [wire.StartX, wire.StartY],
	                originalLabel: alias.Name,
	                labelType: 'alias',
	            });
	            yield netLabel;
	            const labelPos = [alias.LocX ?? wire.StartX, alias.LocY ?? wire.StartY];
	            const { angle } = OrcadDsnUtils_1.OrcadDsnUtils.orcadRotationToCadstrom(alias.Rotation ?? 0, 0);
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
	                drawing: {
	                    type: 'text',
	                    ...(0, ModelText_1.encodeStrippedText)({ raw: alias.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
	                    pos: labelPos,
	                    angle,
	                    justify: [-1, 1],
	                    size: alias.Font ? (0, OrcadDsnSymbolUtils_1.scaleFont)(alias.Font) : 1,
	                    thickness: 1,
	                },
	            }, netLabel);
	        }
	    }
	}
	OrcadDsnTransformNetWireGenerator.OrcadDsnTransformNetWireGenerator = OrcadDsnTransformNetWireGenerator$1;
	
	return OrcadDsnTransformNetWireGenerator;
}

var OrcadDsnTransformPortGenerator = {};

var orcadIntersheetReferences = {};

var hasRequiredOrcadIntersheetReferences;

function requireOrcadIntersheetReferences () {
	if (hasRequiredOrcadIntersheetReferences) return orcadIntersheetReferences;
	hasRequiredOrcadIntersheetReferences = 1;
	Object.defineProperty(orcadIntersheetReferences, "__esModule", { value: true });
	orcadIntersheetReferences.OrCadIntersheetReferenceSource = orcadIntersheetReferences.OrCadIntersheetObjectKind = void 0;
	var OrCadIntersheetObjectKind;
	(function (OrCadIntersheetObjectKind) {
	    OrCadIntersheetObjectKind["Port"] = "port";
	    OrCadIntersheetObjectKind["OffPageConnector"] = "off_page_connector";
	})(OrCadIntersheetObjectKind || (orcadIntersheetReferences.OrCadIntersheetObjectKind = OrCadIntersheetObjectKind = {}));
	var OrCadIntersheetReferenceSource;
	(function (OrCadIntersheetReferenceSource) {
	    OrCadIntersheetReferenceSource["Stored"] = "stored";
	    OrCadIntersheetReferenceSource["Computed"] = "computed";
	})(OrCadIntersheetReferenceSource || (orcadIntersheetReferences.OrCadIntersheetReferenceSource = OrCadIntersheetReferenceSource = {}));
	
	return orcadIntersheetReferences;
}

var hasRequiredOrcadDsnTransformPortGenerator;

function requireOrcadDsnTransformPortGenerator () {
	if (hasRequiredOrcadDsnTransformPortGenerator) return OrcadDsnTransformPortGenerator;
	hasRequiredOrcadDsnTransformPortGenerator = 1;
	Object.defineProperty(OrcadDsnTransformPortGenerator, "__esModule", { value: true });
	OrcadDsnTransformPortGenerator.OrcadDsnTransformPortGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformNetLabel_1 = requireTransformNetLabel();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const OrcadDsnBusUtils_1 = requireOrcadDsnBusUtils();
	const portOccurrenceNormalization_1 = requirePortOccurrenceNormalization();
	const orcadIntersheetReferences_1 = requireOrcadIntersheetReferences();
	const OrcadDsnPortOccurrenceUtils_1 = requireOrcadDsnPortOccurrenceUtils();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	class OrcadDsnTransformPortGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	    }
	    *generate(parent, rootContext) {
	        const page = parent.hierarchyEntry.page;
	        const design = parent.hierarchyEntry.design;
	        const reportOccurrenceDiagnostic = (message) => {
	            parent.context.hierarchyPortDiagnostics = parent.context.hierarchyPortDiagnostics ?? new Set();
	            parent.context.hierarchyPortDiagnostics.add(message);
	        };
	        for (const port of page.Port ?? []) {
	            const symbolName = port.SymbolName ?? port.Name;
	            const variantResolution = (0, OrcadDsnSymbolUtils_1.resolveSymbolVariant)(design.Cache?.PortSymbol, symbolName, port.LibName);
	            if (variantResolution.ambiguous) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'symbol-variant-ambiguous',
	                    message: `[symbol-variant-ambiguous] port symbol="${symbolName}" library="${port.LibName ?? ''}" does not select exactly one sourced cache entry; withheld`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	                continue;
	            }
	            const pinPlacement = (0, OrcadDsnSymbolUtils_1.computePortPinPosition)(design, port);
	            const pinPosition = pinPlacement.position;
	            if (!pinPlacement.pinRecorded) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-port-pin-unrecorded',
	                    message: `[port-pin-unrecorded] port="${port.Name}" symbol="${symbolName}" has no cache pin record, so its connection point rests on the port's own origin`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(port.Name);
	            const occurrenceMembers = (0, OrcadDsnPortOccurrenceUtils_1.childPortMemberOccurrenceBindings)(parent.instance, port.Name, reportOccurrenceDiagnostic);
	            const members = declaredMembers.length > 0 && declaredMembers.length === occurrenceMembers.length ? declaredMembers
	                : occurrenceMembers.length > 0 ?
	                    occurrenceMembers.map((binding, declarationOrdinal) => ({
	                        name: binding.memberName,
	                        index: binding.identity.literalBitIndex ?? declarationOrdinal,
	                    }))
	                    : declaredMembers;
	            const portOccurrence = (0, OrcadDsnPortOccurrenceUtils_1.childPortOccurrenceBindings)(parent.instance, port.Name, reportOccurrenceDiagnostic)[0];
	            const hierarchyPortName = portOccurrence?.memberName ?? port.Name;
	            const portStrategyMetadata = {
	                'hierarchical-port': {
	                    portDbId: port.DbId.toString(),
	                    isPowerPort: port.Type === 7,
	                },
	                ...(portOccurrence ? { 'port-occurrence': portOccurrence.identity } : {}),
	            };
	            const netLabel = new TransformNetLabel_1.TransformNetLabel(parent, {
	                position: pinPosition,
	                originalLabel: hierarchyPortName,
	                labelType: 'hierarchical-port',
	                dimensions: members.length ? ['bus'] : undefined,
	                strategyMetadata: portStrategyMetadata,
	            });
	            yield netLabel;
	            rootContext.portNetLabelRecords = rootContext.portNetLabelRecords ?? [];
	            rootContext.portNetLabelRecords.push({ page, portDbId: port.DbId, label: netLabel });
	            if (members.length) {
	                for (let memberOrdinal = 0; memberOrdinal < members.length; memberOrdinal++) {
	                    const busMember = members[memberOrdinal];
	                    const occurrenceIdentity = occurrenceMembers.find((binding) => binding.identity.declarationOrdinal === memberOrdinal)?.identity ??
	                        (0, OrcadDsnPortOccurrenceUtils_1.childPortOccurrenceBindings)(parent.instance, busMember.name, reportOccurrenceDiagnostic)[0]?.identity;
	                    const dimensionName = (0, OrcadDsnBusUtils_1.busDimensionName)(busMember);
	                    const strategyMetadata = {
	                        'hierarchical-port': {
	                            portDbId: port.DbId.toString(),
	                        },
	                        'bus-member': {
	                            busName: port.Name,
	                            memberName: busMember.name,
	                            memberIndex: busMember.index,
	                        },
	                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
	                    };
	                    yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                        position: pinPosition,
	                        originalLabel: busMember.name,
	                        labelType: 'hierarchical-port',
	                        dimensions: [dimensionName],
	                        strategyMetadata,
	                    });
	                }
	            }
	            const symbolDef = (0, OrcadDsnSymbolUtils_1.findPortSymbol)(design, port);
	            if (symbolDef) {
	                const { drawing, imageExtractions } = (0, OrcadDsnSymbolUtils_1.generateSymbolGroupDrawing)({
	                    parent,
	                    symbolDef,
	                    boundingBox: symbolDef.BoundingBox,
	                    locX: port.LocX,
	                    locY: port.LocY,
	                    rotation: port.Rotation ?? 0,
	                    mirror: port.Mirror ?? 0,
	                    name: port.Name,
	                    displayProps: port.PortDisplayProp,
	                    userProps: port.PortUserProp,
	                    netRefObject: netLabel,
	                    crossReferenceSource: { kind: orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port, dbId: port.DbId },
	                });
	                yield drawing;
	                yield* imageExtractions;
	            }
	        }
	    }
	}
	OrcadDsnTransformPortGenerator.OrcadDsnTransformPortGenerator = OrcadDsnTransformPortGenerator$1;
	
	return OrcadDsnTransformPortGenerator;
}

var OrcadDsnTransformSheetSymbolGenerator = {};

var TransformSchematicReference = {};

var hasRequiredTransformSchematicReference;

function requireTransformSchematicReference () {
	if (hasRequiredTransformSchematicReference) return TransformSchematicReference;
	hasRequiredTransformSchematicReference = 1;
	Object.defineProperty(TransformSchematicReference, "__esModule", { value: true });
	TransformSchematicReference.TransformSchematicReference = void 0;
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	const TransformMultiInstanceSchematic_1 = requireTransformMultiInstanceSchematic();
	class TransformSchematicReference$1 extends TransformSchematicDrawing_1.TransformSchematicDrawing {
	    constructor(parent, props) {
	        super(parent, {
	            layers: props.layers,
	            drawing: {
	                type: 'schref',
	                name: props.name,
	                file: props.file,
	                designId: null,
	                drawModule: { ref: String(props.transformModule.deduplicationKey) },
	            },
	        });
	        this._transformModule = props.transformModule;
	        this._targetSchematic = props.targetSchematic;
	        this._layers = props.layers;
	        this._name = props.name;
	        this._file = props.file;
	    }
	    get dependencies() {
	        const deps = [this._transformModule];
	        if (this._targetSchematic) {
	            deps.push(this._targetSchematic);
	        }
	        return deps;
	    }
	    compile() {
	        let designId = null;
	        if (this._targetSchematic) {
	            const targetParent = this._targetSchematic.parent;
	            if (targetParent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	                designId = targetParent.designId;
	            }
	            else {
	                designId = this._targetSchematic.designId;
	            }
	        }
	        return {
	            layers: this._layers,
	            drawing: {
	                type: 'schref',
	                name: this._name,
	                file: this._file,
	                designId,
	                drawModule: this._transformModule.compiled,
	            },
	        };
	    }
	}
	TransformSchematicReference.TransformSchematicReference = TransformSchematicReference$1;
	
	return TransformSchematicReference;
}

var hasRequiredOrcadDsnTransformSheetSymbolGenerator;

function requireOrcadDsnTransformSheetSymbolGenerator () {
	if (hasRequiredOrcadDsnTransformSheetSymbolGenerator) return OrcadDsnTransformSheetSymbolGenerator;
	hasRequiredOrcadDsnTransformSheetSymbolGenerator = 1;
	Object.defineProperty(OrcadDsnTransformSheetSymbolGenerator, "__esModule", { value: true });
	OrcadDsnTransformSheetSymbolGenerator.OrcadDsnTransformSheetSymbolGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformNetLabel_1 = requireTransformNetLabel();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const TransformSchematicReference_1 = requireTransformSchematicReference();
	const TransformModule_1 = requireTransformModule();
	const TransformComponent_1 = requireTransformComponent();
	const TransformComponentSymbol_1 = requireTransformComponentSymbol();
	const TransformProjectModule_1 = requireTransformProjectModule();
	const TransformProject_1 = requireTransformProject();
	const TransformObject_1 = requireTransformObject();
	const orcadLayers_1 = requireOrcadLayers();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const OrcadDsnBusUtils_1 = requireOrcadDsnBusUtils();
	const OrcadDsnUtils_2 = requireOrcadDsnUtils();
	const OrcadDsnPortOccurrenceUtils_1 = requireOrcadDsnPortOccurrenceUtils();
	const ModelText_1 = requireModelText();
	const DEFAULT_SHEET_SYMBOL_WIDTH = 200;
	const DEFAULT_SHEET_SYMBOL_HEIGHT = 100;
	class OrcadDsnTransformSheetSymbolGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	    }
	    *generate(parent, _rootContext) {
	        const page = parent.hierarchyEntry.page;
	        const busPositionLookup = (0, OrcadDsnBusUtils_1.buildBusPositionLookup)(page);
	        const reportOccurrenceDiagnostic = (message) => {
	            parent.context.hierarchyPortDiagnostics = parent.context.hierarchyPortDiagnostics ?? new Set();
	            parent.context.hierarchyPortDiagnostics.add(message);
	        };
	        for (const drawnInst of page.DrawnInst ?? []) {
	            if (!drawnInst.ContentsLibName && !drawnInst.ContentsViewName) {
	                continue;
	            }
	            const drawnInstMeta = {
	                'hierarchical-sheet-entry': { sheetSymbolDbId: drawnInst.DbId.toString() },
	            };
	            for (const portInst of drawnInst.PortInstScalar ?? []) {
	                const pinPosition = [drawnInst.LocX + portInst.HotptX, drawnInst.LocY + portInst.HotptY];
	                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, drawnInst.DbId.toString(), portInst.Name, reportOccurrenceDiagnostic);
	                const pinLabel = new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: pinPosition,
	                    originalLabel: portInst.Name,
	                    labelType: 'hierarchical-sheet-entry',
	                    strategyMetadata: {
	                        ...drawnInstMeta,
	                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
	                    },
	                });
	                yield pinLabel;
	            }
	            for (const portInst of drawnInst.PortInstBus ?? []) {
	                const pinPosition = [drawnInst.LocX + portInst.HotptX, drawnInst.LocY + portInst.HotptY];
	                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, drawnInst.DbId.toString(), portInst.Name, reportOccurrenceDiagnostic);
	                const occurrenceBindings = (0, OrcadDsnPortOccurrenceUtils_1.parentPortOccurrenceBindings)(parent.instance, drawnInst.DbId.toString(), portInst.Name, portInst.Name, reportOccurrenceDiagnostic);
	                const portMembers = this.portMembersFromBindings(portInst.Name, occurrenceBindings);
	                yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: pinPosition,
	                    originalLabel: portInst.Name,
	                    labelType: 'hierarchical-sheet-entry',
	                    strategyMetadata: {
	                        ...drawnInstMeta,
	                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
	                        'port-bus': { name: portInst.Name, members: portMembers },
	                    },
	                    dimensions: ['bus'],
	                });
	                yield* this.generateBusPortMemberLabels(pinPosition, portInst.Name, drawnInstMeta, busPositionLookup, parent, occurrenceBindings);
	            }
	            yield* this.generateSchRef(parent, drawnInst);
	        }
	        for (const partInst of page.PartInst ?? []) {
	            if (!(0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(partInst)) {
	                continue;
	            }
	            if ((0, OrcadDsnUtils_1.unresolvedBlockIsPhysicalPart)(parent.instance.children.some((child) => child.sheetSymbol === partInst), parent.hierarchyEntry.design, partInst)) {
	                continue;
	            }
	            if (partInst.ContentsLibName.length === 0) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-sheet-reference-without-library',
	                    message: `[sheet-reference-without-library] ${partInst.Reference}: hierarchical block names view "${partInst.ContentsViewName}" but no library file`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            const partInstMeta = {
	                'hierarchical-sheet-entry': { sheetSymbolDbId: partInst.DbId.toString() },
	            };
	            for (const portInst of partInst.PortInstScalar ?? []) {
	                const pinPosition = this.portInstancePosition(partInst, portInst);
	                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, partInst.DbId.toString(), portInst.Position, reportOccurrenceDiagnostic);
	                yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: pinPosition,
	                    originalLabel: portInst.Name,
	                    labelType: 'hierarchical-sheet-entry',
	                    strategyMetadata: {
	                        ...partInstMeta,
	                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
	                    },
	                });
	            }
	            for (const portInst of partInst.PortInstBus ?? []) {
	                const pinPosition = this.portInstancePosition(partInst, portInst);
	                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, partInst.DbId.toString(), portInst.Position, reportOccurrenceDiagnostic);
	                const occurrenceBindings = (0, OrcadDsnPortOccurrenceUtils_1.parentPortOccurrenceBindings)(parent.instance, partInst.DbId.toString(), portInst.Position, portInst.Name, reportOccurrenceDiagnostic);
	                const portMembers = this.portMembersFromBindings(portInst.Name, occurrenceBindings);
	                yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position: pinPosition,
	                    originalLabel: portInst.Name,
	                    labelType: 'hierarchical-sheet-entry',
	                    strategyMetadata: {
	                        ...partInstMeta,
	                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
	                        'port-bus': { name: portInst.Name, members: portMembers },
	                    },
	                    dimensions: ['bus'],
	                });
	                yield* this.generateBusPortMemberLabels(pinPosition, portInst.Name, partInstMeta, busPositionLookup, parent, occurrenceBindings);
	            }
	            yield* this.generateSchRefFromPartInst(parent, partInst);
	        }
	    }
	    portInstancePosition(partInst, portInst) {
	        if (portInst.RecordedX !== undefined && portInst.RecordedY !== undefined) {
	            return [portInst.RecordedX, portInst.RecordedY];
	        }
	        return [partInst.LocX + portInst.HotptX, partInst.LocY + portInst.HotptY];
	    }
	    *generateBusPortMemberLabels(pinPosition, busPortName, metadata, busPositionLookup, parent, occurrenceBindings) {
	        const parentBus = busPositionLookup(`${pinPosition[0]},${pinPosition[1]}`);
	        const memberBindings = (this.supportsOccurrenceMemberBinding(occurrenceBindings) &&
	            (0, OrcadDsnPortOccurrenceUtils_1.coversCompleteDeclaredBusRange)(busPortName, occurrenceBindings)) ?
	            occurrenceBindings
	            : [];
	        const portMembers = this.portMembersFromBindings(busPortName, memberBindings);
	        if (portMembers.length === 0) {
	            return;
	        }
	        const occurrenceSuffixes = memberBindings.map((binding) => binding.parentMemberName?.match(/(\d+)$/)?.[1]);
	        const hasUniqueOccurrenceSuffixes = occurrenceSuffixes.length > 0 &&
	            occurrenceSuffixes.every((suffix) => suffix !== undefined) &&
	            new Set(occurrenceSuffixes).size === occurrenceSuffixes.length;
	        const usePositionalDim = parentBus !== undefined &&
	            (memberBindings.length > 0 ?
	                hasUniqueOccurrenceSuffixes
	                : !(0, OrcadDsnBusUtils_1.busPortAlignsWithParent)(portMembers, parentBus.members, parentBus.name));
	        for (let i = 0; i < portMembers.length; i++) {
	            const member = portMembers[i];
	            const occurrenceBinding = (0, OrcadDsnPortOccurrenceUtils_1.occurrenceBindingForDeclaredBusMember)(occurrenceBindings, i, member.index, portMembers.length);
	            const dimension = usePositionalDim ? (0, OrcadDsnBusUtils_1.busPositionalDimensionName)(i) : (0, OrcadDsnBusUtils_1.busDimensionName)(member);
	            yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                position: pinPosition,
	                originalLabel: member.name,
	                labelType: 'hierarchical-sheet-entry',
	                strategyMetadata: {
	                    ...metadata,
	                    'bus-member': { busName: busPortName, memberName: member.name, memberIndex: member.index },
	                    ...(occurrenceBinding ? { 'port-occurrence': occurrenceBinding.identity } : {}),
	                },
	                dimensions: [dimension],
	            });
	        }
	    }
	    portMembersFromBindings(busPortName, bindings) {
	        return (0, OrcadDsnPortOccurrenceUtils_1.busPortMembersFromOccurrenceBindings)(busPortName, this.supportsOccurrenceMemberBinding(bindings) ? bindings : []);
	    }
	    supportsOccurrenceMemberBinding(bindings) {
	        const parts = bindings.map((binding) => binding.parentMemberName?.match(/^(.*?)(\d+)$/));
	        if (parts.length === 0 ||
	            parts.some((part) => !part) ||
	            bindings.some((binding) => binding.identity.literalBitIndex === undefined)) {
	            return false;
	        }
	        const prefixes = new Set(parts.map((part) => part?.[1].toUpperCase()));
	        const suffixes = new Set(parts.map((part) => part?.[2]));
	        return prefixes.size === 1 && suffixes.size === parts.length;
	    }
	    *generateSchRef(parent, drawnInst) {
	        const symbolLayer = orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id;
	        const sheetWidth = drawnInst.X2 - drawnInst.X1;
	        const sheetHeight = drawnInst.Y2 - drawnInst.Y1;
	        const compName = `schref_${drawnInst.DbId}`;
	        const instance = parent.instance;
	        const moduleRef = `${instance.pathUniqueId}::schref::${drawnInst.DbId}`;
	        const centerX = sheetWidth / 2;
	        const centerY = sheetHeight / 2;
	        const pins = [];
	        for (const portInst of [...(drawnInst.PortInstScalar ?? []), ...(drawnInst.PortInstBus ?? [])]) {
	            if (portInst.DbId === undefined) {
	                continue;
	            }
	            const relX = portInst.HotptX - centerX;
	            const relY = portInst.HotptY - centerY;
	            const onLeftEdge = portInst.HotptX <= drawnInst.X1;
	            const onRightEdge = portInst.HotptX >= drawnInst.X2;
	            const onTopEdge = portInst.HotptY <= drawnInst.Y1;
	            const onBottomEdge = portInst.HotptY >= drawnInst.Y2;
	            const pinStubLength = 5;
	            const stubEnd = onLeftEdge ? [relX - pinStubLength, relY] : [relX + pinStubLength, relY];
	            let nameJustify = onLeftEdge ? [-1, 0] : [1, 0];
	            let angle = 0;
	            let labelPos = [relX, relY];
	            const markerDepth = 8;
	            const spacing = markerDepth + 3;
	            if (onLeftEdge) {
	                labelPos = [relX + spacing, relY];
	                nameJustify = [-1, 0];
	            }
	            else if (onRightEdge) {
	                labelPos = [relX - spacing, relY];
	                nameJustify = [1, 0];
	            }
	            else if (onTopEdge) {
	                angle = 90;
	                labelPos = [relX, relY + spacing];
	                nameJustify = [1, 0];
	            }
	            else if (onBottomEdge) {
	                angle = 90;
	                labelPos = [relX, relY - spacing];
	                nameJustify = [-1, 0];
	            }
	            const edge = onLeftEdge ? 'left'
	                : onRightEdge ? 'right'
	                    : onTopEdge ? 'top'
	                        : 'bottom';
	            const markerPoints = this.getPortMarkerPoints(portInst.Type, relX, relY, edge, markerDepth, markerDepth / 2);
	            const pinDrawings = [
	                {
	                    layers: [symbolLayer],
	                    drawing: {
	                        type: 'text',
	                        ...(0, ModelText_1.encodeStrippedText)({ raw: portInst.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
	                        pos: labelPos,
	                        angle,
	                        justify: nameJustify,
	                        size: 4,
	                        thickness: 1,
	                    },
	                },
	            ];
	            if (markerPoints) {
	                pinDrawings.push({
	                    layers: [symbolLayer],
	                    drawing: {
	                        type: 'polygon',
	                        points: markerPoints,
	                        filled: true,
	                        width: 0,
	                    },
	                });
	            }
	            pins.push({
	                type: 'pin',
	                ref: `${portInst.Name}#${portInst.DbId}`,
	                name: portInst.Name,
	                number: `${portInst.Name}#${portInst.DbId}`,
	                start: [relX, relY],
	                end: stubEnd,
	                layer: symbolLayer,
	                width: 1,
	                drawings: pinDrawings,
	            });
	        }
	        const symbolDrawings = [
	            {
	                layers: [symbolLayer],
	                drawing: {
	                    type: 'rect',
	                    start: [-centerX, -centerY],
	                    end: [centerX, centerY],
	                    strokeWidth: 1,
	                    filled: false,
	                },
	            },
	        ];
	        const component = new TransformComponent_1.TransformComponent(parent, {
	            baseComponent: {
	                name: compName,
	                isVirtual: true,
	            },
	        });
	        yield component;
	        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(component, {
	            ref: compName,
	            name: compName,
	            pins,
	            drawings: symbolDrawings,
	            metadata: {},
	        });
	        yield symbol;
	        const drawProperties = {};
	        const namedValues = {
	            Reference: drawnInst.DeviceDesignator || drawnInst.Reference,
	            Value: drawnInst.PartValue || drawnInst.ContentsViewName,
	        };
	        for (const userProp of drawnInst.UserProperty ?? []) {
	            namedValues[userProp.Name] = userProp.Value;
	        }
	        const locX = drawnInst.LocX;
	        const locY = drawnInst.LocY;
	        for (const displayProp of drawnInst.DisplayProperty ?? []) {
	            const namedValue = namedValues[displayProp.Name];
	            if (namedValue === undefined) {
	                continue;
	            }
	            const fontSize = (0, OrcadDsnSymbolUtils_1.scaleFont)(displayProp.Font);
	            drawProperties[displayProp.Name] = {
	                ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
	                pos: [locX + displayProp.LocX, locY + displayProp.LocY],
	                layer: symbolLayer,
	                angle: displayProp.Rotation * 90,
	                justify: [-1, -1],
	                size: fontSize,
	                thickness: 1,
	                hidden: false,
	                mirror: [false, false],
	            };
	        }
	        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
	        const projectModuleName = namedValues['Reference'] ?? drawnInst.Name;
	        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
	            name: projectModuleName,
	            component,
	            hideInBom: true,
	        });
	        yield projectModule;
	        const moduleCenter = [locX + centerX, locY + centerY];
	        const transformModule = new TransformModule_1.TransformModule(parent, {
	            ref: moduleRef,
	            footprintRef: compName,
	            center: moduleCenter,
	            transform: { pos: [0, 0], angle: 0, mirrored: [false, false] },
	            name: projectModuleName,
	            fullModuleName: projectModuleName,
	            drawProperties,
	            metadata: {
	                'orcad.drawnInst.dbId': drawnInst.DbId,
	                'orcad.drawnInst.contentsViewName': drawnInst.ContentsViewName,
	                'orcad.drawnInst.contentsLibName': drawnInst.ContentsLibName,
	            },
	        }, component, projectModule);
	        yield transformModule;
	        const context = parent.context;
	        const childInstance = instance.children.find((child) => child.sheetSymbol && child.sheetSymbol.DbId === drawnInst.DbId);
	        const targetSchematic = childInstance ? (context.instanceSchematicMap.get(childInstance) ?? null) : null;
	        yield new TransformSchematicReference_1.TransformSchematicReference(parent, {
	            layers: [symbolLayer],
	            name: namedValues['Reference'] ?? drawnInst.Name,
	            file: drawnInst.ContentsLibName || drawnInst.ContentsViewName,
	            transformModule,
	            targetSchematic,
	        });
	    }
	    *generateSchRefFromPartInst(parent, partInst) {
	        const design = parent.hierarchyEntry.design;
	        let sheetWidth;
	        let sheetHeight;
	        const resolved = this.resolvePartInstBoundingBox(design, partInst);
	        if (resolved) {
	            sheetWidth = resolved.X2 - resolved.X1;
	            sheetHeight = resolved.Y2 - resolved.Y1;
	        }
	        else {
	            const allPorts = [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])];
	            if (allPorts.length === 0) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-sheet-symbol-default-geometry',
	                    message: `[sheet-symbol-default-geometry] ${partInst.Reference}: hierarchical block has neither a cached bounding box nor ports, so it is drawn at ${DEFAULT_SHEET_SYMBOL_WIDTH}x${DEFAULT_SHEET_SYMBOL_HEIGHT}`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	                sheetWidth = DEFAULT_SHEET_SYMBOL_WIDTH;
	                sheetHeight = DEFAULT_SHEET_SYMBOL_HEIGHT;
	            }
	            else {
	                const xs = allPorts.map((p) => p.HotptX);
	                const ys = allPorts.map((p) => p.HotptY);
	                const padding = 20;
	                sheetWidth = Math.max(...xs) - Math.min(...xs) + padding * 2;
	                sheetHeight = Math.max(...ys) - Math.min(...ys) + padding * 2;
	            }
	        }
	        const symbolLayer = orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id;
	        const compName = `schref_${partInst.DbId}`;
	        const instance = parent.instance;
	        const moduleRef = `${instance.pathUniqueId}::schref::${partInst.DbId}`;
	        const centerX = sheetWidth / 2;
	        const centerY = sheetHeight / 2;
	        const pins = [];
	        for (const portInst of [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])]) {
	            const relX = portInst.HotptX - centerX;
	            const relY = portInst.HotptY - centerY;
	            const onLeftEdge = portInst.HotptX <= 0;
	            const onRightEdge = portInst.HotptX >= sheetWidth;
	            const onTopEdge = portInst.HotptY <= 0;
	            const pinStubLength = 5;
	            const stubEnd = onLeftEdge ? [relX - pinStubLength, relY] : [relX + pinStubLength, relY];
	            let nameJustify = onLeftEdge ? [-1, 0] : [1, 0];
	            let angle = 0;
	            let labelPos = [relX, relY];
	            const markerDepth = 8;
	            const spacing = markerDepth + 3;
	            if (onLeftEdge) {
	                labelPos = [relX + spacing, relY];
	                nameJustify = [-1, 0];
	            }
	            else if (onRightEdge) {
	                labelPos = [relX - spacing, relY];
	                nameJustify = [1, 0];
	            }
	            else if (onTopEdge) {
	                angle = 90;
	                labelPos = [relX, relY + spacing];
	                nameJustify = [1, 0];
	            }
	            else {
	                angle = 90;
	                labelPos = [relX, relY - spacing];
	                nameJustify = [-1, 0];
	            }
	            const edge = onLeftEdge ? 'left'
	                : onRightEdge ? 'right'
	                    : onTopEdge ? 'top'
	                        : 'bottom';
	            const markerPoints = this.getPortMarkerPoints(portInst.Type, relX, relY, edge, markerDepth, markerDepth / 2);
	            const pinDrawings = [
	                {
	                    layers: [symbolLayer],
	                    drawing: {
	                        type: 'text',
	                        ...(0, ModelText_1.encodeStrippedText)({ raw: portInst.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
	                        pos: labelPos,
	                        angle,
	                        justify: nameJustify,
	                        size: 4,
	                        thickness: 1,
	                    },
	                },
	            ];
	            if (markerPoints) {
	                pinDrawings.push({
	                    layers: [symbolLayer],
	                    drawing: {
	                        type: 'polygon',
	                        points: markerPoints,
	                        filled: true,
	                        width: 0,
	                    },
	                });
	            }
	            pins.push({
	                type: 'pin',
	                ref: `${portInst.Name}#${portInst.DbId}`,
	                name: portInst.Name,
	                number: `${portInst.Name}#${portInst.DbId}`,
	                start: [relX, relY],
	                end: stubEnd,
	                layer: symbolLayer,
	                width: 1,
	                drawings: pinDrawings,
	            });
	        }
	        const symbolDrawings = [
	            {
	                layers: [symbolLayer],
	                drawing: {
	                    type: 'rect',
	                    start: [-centerX, -centerY],
	                    end: [centerX, centerY],
	                    strokeWidth: 1,
	                    filled: false,
	                },
	            },
	        ];
	        const component = new TransformComponent_1.TransformComponent(parent, {
	            baseComponent: {
	                name: compName,
	                isVirtual: true,
	            },
	        });
	        yield component;
	        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(component, {
	            ref: compName,
	            name: compName,
	            pins,
	            drawings: symbolDrawings,
	            metadata: {},
	        });
	        yield symbol;
	        const drawProperties = {};
	        const namedValues = {
	            Reference: partInst.DeviceDesignator || partInst.Reference,
	            Value: partInst.PartValue || partInst.ContentsViewName,
	        };
	        for (const userProp of partInst.UserProperty ?? []) {
	            namedValues[userProp.Name] = userProp.Value;
	        }
	        const locX = partInst.LocX;
	        const locY = partInst.LocY;
	        for (const displayProp of partInst.DisplayProperty ?? []) {
	            const namedValue = namedValues[displayProp.Name];
	            if (namedValue === undefined) {
	                continue;
	            }
	            const fontSize = (0, OrcadDsnSymbolUtils_1.scaleFont)(displayProp.Font);
	            drawProperties[displayProp.Name] = {
	                ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
	                pos: [locX + displayProp.LocX, locY + displayProp.LocY],
	                layer: symbolLayer,
	                angle: displayProp.Rotation * 90,
	                justify: [-1, -1],
	                size: fontSize,
	                thickness: 1,
	                hidden: false,
	                mirror: [false, false],
	            };
	        }
	        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
	        const projectModuleName = namedValues['Reference'] ?? partInst.Name;
	        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
	            name: projectModuleName,
	            component,
	            hideInBom: true,
	        });
	        yield projectModule;
	        const moduleCenter = [locX + centerX, locY + centerY];
	        const transformModule = new TransformModule_1.TransformModule(parent, {
	            ref: moduleRef,
	            footprintRef: compName,
	            center: moduleCenter,
	            transform: { pos: [0, 0], angle: 0, mirrored: [false, false] },
	            name: projectModuleName,
	            fullModuleName: projectModuleName,
	            drawProperties,
	            metadata: {
	                'orcad.partInst.dbId': partInst.DbId,
	                'orcad.partInst.contentsViewName': partInst.ContentsViewName,
	                'orcad.partInst.contentsLibName': partInst.ContentsLibName,
	            },
	        }, component, projectModule);
	        yield transformModule;
	        const context = parent.context;
	        const childInstance = instance.children.find((child) => child.sheetSymbol && child.sheetSymbol.DbId === partInst.DbId);
	        const targetSchematic = childInstance ? (context.instanceSchematicMap.get(childInstance) ?? null) : null;
	        yield new TransformSchematicReference_1.TransformSchematicReference(parent, {
	            layers: [symbolLayer],
	            name: namedValues['Reference'] ?? partInst.Name,
	            file: partInst.ContentsLibName || partInst.ContentsViewName,
	            transformModule,
	            targetSchematic,
	        });
	    }
	    resolvePartInstBoundingBox(design, partInst) {
	        const cache = design.Cache;
	        if (!cache) {
	            return undefined;
	        }
	        const packageResolution = OrcadDsnUtils_2.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
	        if (!packageResolution) {
	            return undefined;
	        }
	        const { pkg } = packageResolution;
	        for (const libPart of pkg.LibPart) {
	            for (const normalView of libPart.NormalView ?? []) {
	                const ref = `${libPart.CellName}${normalView.Suffix}`;
	                if (ref === partInst.GraphicName) {
	                    return normalView.BoundingBox;
	                }
	            }
	        }
	        return undefined;
	    }
	    getPortMarkerPoints(portType, relX, relY, edge, depth, halfWidth) {
	        let ix, iy, ax, ay;
	        switch (edge) {
	            case 'left':
	                ix = 1;
	                iy = 0;
	                ax = 0;
	                ay = 1;
	                break;
	            case 'right':
	                ix = -1;
	                iy = 0;
	                ax = 0;
	                ay = 1;
	                break;
	            case 'top':
	                ix = 0;
	                iy = 1;
	                ax = 1;
	                ay = 0;
	                break;
	            case 'bottom':
	                ix = 0;
	                iy = -1;
	                ax = 1;
	                ay = 0;
	                break;
	        }
	        const d = depth;
	        const hw = halfWidth;
	        const tail = d * 0.5;
	        switch (portType) {
	            case 0:
	                return [
	                    [relX - hw * ax, relY - hw * ay],
	                    [relX + tail * ix - hw * ax, relY + tail * iy - hw * ay],
	                    [relX + d * ix, relY + d * iy],
	                    [relX + tail * ix + hw * ax, relY + tail * iy + hw * ay],
	                    [relX + hw * ax, relY + hw * ay],
	                ];
	            case 1:
	                return [
	                    [relX, relY],
	                    [relX + tail * ix - hw * ax, relY + tail * iy - hw * ay],
	                    [relX + d * ix, relY + d * iy],
	                    [relX + tail * ix + hw * ax, relY + tail * iy + hw * ay],
	                ];
	            case 2:
	                return [
	                    [relX, relY],
	                    [relX + tail * ix - hw * ax, relY + tail * iy - hw * ay],
	                    [relX + d * ix - hw * ax, relY + d * iy - hw * ay],
	                    [relX + d * ix + hw * ax, relY + d * iy + hw * ay],
	                    [relX + tail * ix + hw * ax, relY + tail * iy + hw * ay],
	                ];
	            default:
	                return null;
	        }
	    }
	}
	OrcadDsnTransformSheetSymbolGenerator.OrcadDsnTransformSheetSymbolGenerator = OrcadDsnTransformSheetSymbolGenerator$1;
	
	return OrcadDsnTransformSheetSymbolGenerator;
}

var OrcadDsnTransformOffPageConnectorGenerator = {};

var hasRequiredOrcadDsnTransformOffPageConnectorGenerator;

function requireOrcadDsnTransformOffPageConnectorGenerator () {
	if (hasRequiredOrcadDsnTransformOffPageConnectorGenerator) return OrcadDsnTransformOffPageConnectorGenerator;
	hasRequiredOrcadDsnTransformOffPageConnectorGenerator = 1;
	Object.defineProperty(OrcadDsnTransformOffPageConnectorGenerator, "__esModule", { value: true });
	OrcadDsnTransformOffPageConnectorGenerator.OrcadDsnTransformOffPageConnectorGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformNetLabel_1 = requireTransformNetLabel();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const OrcadDsnBusUtils_1 = requireOrcadDsnBusUtils();
	const portOccurrenceNormalization_1 = requirePortOccurrenceNormalization();
	const orcadIntersheetReferences_1 = requireOrcadIntersheetReferences();
	class OrcadDsnTransformOffPageConnectorGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	    }
	    *generate(parent, _rootContext) {
	        const page = parent.hierarchyEntry.page;
	        const design = parent.hierarchyEntry.design;
	        for (const connector of page.OffPageConnector ?? []) {
	            const resolution = this.findOffPageSymbol(design.Cache?.OffPageSymbol, connector);
	            const symbolDef = resolution.symbol;
	            if (resolution.ambiguous) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'symbol-variant-ambiguous',
	                    message: `[symbol-variant-ambiguous] off-page symbol="${connector.SymbolName}" library="${connector.LibName ?? ''}" does not select exactly one sourced cache entry; withheld`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	                continue;
	            }
	            if (!symbolDef) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-off-page-connector-missing-symbol',
	                    message: `Could not find off-page symbol definition for "${connector.SymbolName}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	                continue;
	            }
	            const pins = symbolDef.PinScalar;
	            if (pins.length === 0) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-off-page-connector-missing-pin',
	                    message: `Off-page symbol definition for "${connector.SymbolName}" has no pin`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	                continue;
	            }
	            if (pins.length > 1) {
	                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                    type: 'orcad-dsn-multi-pin-connector-assumed',
	                    message: `[multi-pin-connector-assumed] off-page connector "${connector.Name}" draws ${pins.length} connection points; every one carries the connector's own net`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            const busDetails = (0, OrcadDsnBusUtils_1.getBusDetails)(connector.Name);
	            const netLabelPositions = pins.map((connectorPin) => (0, OrcadDsnSymbolUtils_1.computeSymbolPinPosition)([connectorPin.HotptX, connectorPin.HotptY], symbolDef.BoundingBox, connector.LocX, connector.LocY, connector.Rotation ?? 0, connector.Mirror ?? 0));
	            const connectorMetadata = {
	                'off-page-connector': { connectorDbId: connector.DbId.toString() },
	            };
	            let netLabel;
	            for (const position of netLabelPositions) {
	                const label = new TransformNetLabel_1.TransformNetLabel(parent, {
	                    position,
	                    originalLabel: connector.Name,
	                    labelType: 'off-page-connector',
	                    dimensions: busDetails ? ['bus'] : undefined,
	                    strategyMetadata: connectorMetadata,
	                });
	                netLabel ??= label;
	                yield label;
	            }
	            if (!netLabel) {
	                continue;
	            }
	            if (busDetails) {
	                const members = (0, portOccurrenceNormalization_1.expandBusPortName)(connector.Name);
	                if (this.shouldExpandOffPageBus(page, members)) {
	                    for (const member of members) {
	                        for (const position of netLabelPositions) {
	                            yield new TransformNetLabel_1.TransformNetLabel(parent, {
	                                position,
	                                originalLabel: member.name,
	                                labelType: 'off-page-connector',
	                                dimensions: [(0, OrcadDsnBusUtils_1.busDimensionName)(member)],
	                                strategyMetadata: connectorMetadata,
	                            });
	                        }
	                    }
	                }
	            }
	            const { drawing, imageExtractions } = (0, OrcadDsnSymbolUtils_1.generateSymbolGroupDrawing)({
	                parent,
	                symbolDef,
	                boundingBox: symbolDef.BoundingBox,
	                locX: connector.LocX,
	                locY: connector.LocY,
	                rotation: connector.Rotation,
	                mirror: connector.Mirror ?? 0,
	                name: connector.Name,
	                displayProps: connector.OffPageConnectorDisplayProp,
	                userProps: connector.OffPageConnectorUserProp,
	                netRefObject: netLabel,
	                crossReferenceSource: { kind: orcadIntersheetReferences_1.OrCadIntersheetObjectKind.OffPageConnector, dbId: connector.DbId },
	            });
	            yield drawing;
	            yield* imageExtractions;
	        }
	    }
	    shouldExpandOffPageBus(page, expandedMembers) {
	        if (expandedMembers.length === 0) {
	            return false;
	        }
	        const expandedNames = new Set(expandedMembers.map((m) => m.name));
	        for (const netBus of page.NetBus ?? []) {
	            for (const ns of netBus.NetScalar ?? []) {
	                if (ns.Name && expandedNames.has(ns.Name)) {
	                    return true;
	                }
	            }
	        }
	        for (const netScalar of page.NetScalar ?? []) {
	            for (const wire of netScalar.WireScalars ?? []) {
	                if ((wire.Alias ?? []).some((alias) => alias.Name !== undefined && expandedNames.has(alias.Name))) {
	                    return true;
	                }
	            }
	        }
	        return false;
	    }
	    findOffPageSymbol(symbols, connector) {
	        const symbolName = connector.SymbolName?.toString();
	        if (!symbols || symbolName === undefined) {
	            return { symbol: undefined, ambiguous: false };
	        }
	        return (0, OrcadDsnSymbolUtils_1.resolveSymbolVariant)(symbols, symbolName, connector.LibName);
	    }
	}
	OrcadDsnTransformOffPageConnectorGenerator.OrcadDsnTransformOffPageConnectorGenerator = OrcadDsnTransformOffPageConnectorGenerator$1;
	
	return OrcadDsnTransformOffPageConnectorGenerator;
}

var OrcadDsnTransformGraphicsGenerator = {};

var orcadImageUtils = {};

var hasRequiredOrcadImageUtils;

function requireOrcadImageUtils () {
	if (hasRequiredOrcadImageUtils) return orcadImageUtils;
	hasRequiredOrcadImageUtils = 1;
	Object.defineProperty(orcadImageUtils, "__esModule", { value: true });
	orcadImageUtils.decodeDibToBmp = decodeDibToBmp;
	const base64_1 = requireBase64$1();
	function decodeDibToBmp(base64Dib) {
	    const cleaned = base64Dib.replace(/\s/g, '');
	    const dib = (0, base64_1.base64ToUint8Array)(cleaned);
	    const dibHeaderSize = dib[0] | (dib[1] << 8) | (dib[2] << 16) | (dib[3] << 24);
	    const bmpFileHeaderSize = 14;
	    const dataOffset = bmpFileHeaderSize + dibHeaderSize;
	    const fileSize = bmpFileHeaderSize + dib.length;
	    const bmp = new Uint8Array(fileSize);
	    bmp[0] = 0x42;
	    bmp[1] = 0x4d;
	    bmp[2] = fileSize & 0xff;
	    bmp[3] = (fileSize >> 8) & 0xff;
	    bmp[4] = (fileSize >> 16) & 0xff;
	    bmp[5] = (fileSize >> 24) & 0xff;
	    bmp[10] = dataOffset & 0xff;
	    bmp[11] = (dataOffset >> 8) & 0xff;
	    bmp[12] = (dataOffset >> 16) & 0xff;
	    bmp[13] = (dataOffset >> 24) & 0xff;
	    bmp.set(dib, bmpFileHeaderSize);
	    return { data: bmp, mimeType: 'image/bmp' };
	}
	
	return orcadImageUtils;
}

var hasRequiredOrcadDsnTransformGraphicsGenerator;

function requireOrcadDsnTransformGraphicsGenerator () {
	if (hasRequiredOrcadDsnTransformGraphicsGenerator) return OrcadDsnTransformGraphicsGenerator;
	hasRequiredOrcadDsnTransformGraphicsGenerator = 1;
	Object.defineProperty(OrcadDsnTransformGraphicsGenerator, "__esModule", { value: true });
	OrcadDsnTransformGraphicsGenerator.OrcadDsnTransformGraphicsGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const orcadLayers_1 = requireOrcadLayers();
	const orcadShapeConverters_1 = requireOrcadShapeConverters();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const orcadImageUtils_1 = requireOrcadImageUtils();
	const imageAssets_1 = requireImageAssets();
	const ModelText_1 = requireModelText();
	class OrcadDsnTransformGraphicsGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	    }
	    *generate(parent, _rootContext) {
	        const page = parent.hierarchyEntry.page;
	        for (const commentText of page.GraphicCommentTextInst ?? []) {
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
	                drawing: {
	                    type: 'text',
	                    ...(0, ModelText_1.encodeStrippedText)({ raw: commentText.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
	                    pos: [commentText.LocX, commentText.LocY],
	                    angle: commentText.Rotation ?? 0,
	                    justify: [-1, 0],
	                    size: (0, OrcadDsnSymbolUtils_1.scaleFont)(commentText.Font),
	                    thickness: 1,
	                },
	            });
	        }
	        const otherLayer = orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Other].id;
	        for (const line of page.GraphicLineInst ?? []) {
	            for (const drawing of orcadShapeConverters_1.OrCadShapeConvert.graphicLineInst()(line)) {
	                yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                    layers: [otherLayer],
	                    drawing,
	                });
	            }
	        }
	        for (const box of page.GraphicBoxInst ?? []) {
	            for (const drawing of orcadShapeConverters_1.OrCadShapeConvert.graphicBoxInst()(box)) {
	                yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                    layers: [otherLayer],
	                    drawing,
	                });
	            }
	        }
	        for (const polygon of page.GraphicPolygonInst ?? []) {
	            for (const drawing of orcadShapeConverters_1.OrCadShapeConvert.graphicPolygonInst()(polygon)) {
	                yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                    layers: [otherLayer],
	                    drawing,
	                });
	            }
	        }
	        for (const polyline of page.GraphicPolylineInst ?? []) {
	            for (const drawing of orcadShapeConverters_1.OrCadShapeConvert.graphicPolylineInst()(polyline)) {
	                yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                    layers: [otherLayer],
	                    drawing,
	                });
	            }
	        }
	        const imageLayer = orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Image].id;
	        for (const bitmap of page.GraphicBitMapInst ?? []) {
	            if (!bitmap.Value) {
	                continue;
	            }
	            const { data, mimeType } = (0, imageAssets_1.decodeBase64Image)(bitmap.Value);
	            const ref = (0, imageAssets_1.computeImageRef)(`bitmap-${bitmap.DbId}`, mimeType);
	            const width = Math.abs(bitmap.X2 - bitmap.X1);
	            const height = Math.abs(bitmap.Y2 - bitmap.Y1);
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [imageLayer],
	                drawing: {
	                    type: 'image',
	                    pos: [Math.min(bitmap.X1, bitmap.X2), Math.min(bitmap.Y1, bitmap.Y2)],
	                    width,
	                    height,
	                    imageRef: ref,
	                },
	            });
	            yield new TransformImageExtraction_1.TransformImageExtraction(parent, ref, `bitmap-${bitmap.DbId}`, data, mimeType);
	        }
	        for (const oleEmbed of page.GraphicOleEmbedInst ?? []) {
	            if (!oleEmbed.Dib) {
	                continue;
	            }
	            const { data, mimeType } = (0, orcadImageUtils_1.decodeDibToBmp)(oleEmbed.Dib);
	            const ref = (0, imageAssets_1.computeImageRef)(`ole-${oleEmbed.DbId}`, mimeType);
	            const width = Math.abs(oleEmbed.X2 - oleEmbed.X1);
	            const height = Math.abs(oleEmbed.Y2 - oleEmbed.Y1);
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
	                layers: [imageLayer],
	                drawing: {
	                    type: 'image',
	                    pos: [Math.min(oleEmbed.X1, oleEmbed.X2), Math.min(oleEmbed.Y1, oleEmbed.Y2)],
	                    width,
	                    height,
	                    imageRef: ref,
	                },
	            });
	            yield new TransformImageExtraction_1.TransformImageExtraction(parent, ref, `ole-${oleEmbed.DbId}`, data, mimeType);
	        }
	    }
	}
	OrcadDsnTransformGraphicsGenerator.OrcadDsnTransformGraphicsGenerator = OrcadDsnTransformGraphicsGenerator$1;
	
	return OrcadDsnTransformGraphicsGenerator;
}

var OrcadDsnTransformGlobalSymbolGenerator = {};

var hasRequiredOrcadDsnTransformGlobalSymbolGenerator;

function requireOrcadDsnTransformGlobalSymbolGenerator () {
	if (hasRequiredOrcadDsnTransformGlobalSymbolGenerator) return OrcadDsnTransformGlobalSymbolGenerator;
	hasRequiredOrcadDsnTransformGlobalSymbolGenerator = 1;
	Object.defineProperty(OrcadDsnTransformGlobalSymbolGenerator, "__esModule", { value: true });
	OrcadDsnTransformGlobalSymbolGenerator.OrcadDsnTransformGlobalSymbolGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformSchematic_1 = requireTransformSchematic();
	const Transform_1 = requireTransform();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const TransformModule_1 = requireTransformModule();
	const TransformMultiInstanceDisplayModule_1 = requireTransformMultiInstanceDisplayModule();
	const TransformMultiInstanceSchematic_1 = requireTransformMultiInstanceSchematic();
	const TransformModulePinConnection_1 = requireTransformModulePinConnection();
	const TransformProcessingResult_1 = requireTransformProcessingResult();
	const TransformComponent_1 = requireTransformComponent();
	const TransformComponentSymbol_1 = requireTransformComponentSymbol();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const TransformProjectModule_1 = requireTransformProjectModule();
	const TransformProject_1 = requireTransformProject();
	const TransformObject_1 = requireTransformObject();
	const orcadLayers_1 = requireOrcadLayers();
	const orcadShapeConverters_1 = requireOrcadShapeConverters();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const orcadLibraryProvenance_1 = requireOrcadLibraryProvenance();
	const ModelText_1 = requireModelText();
	const CADENCE_GROUND_SYMBOL_NAMES = new Map([
	    ['CAPSYM', new Set(['GND', 'GND_CHASSIS', 'GND_EARTH', 'GND_FIELD_SIGNAL', 'GND_POWER', 'GND_SIGNAL'])],
	    ['SOURCE', new Set(['0'])],
	]);
	const PROJECT_GROUND_SYMBOL_PATTERN = /^(?:.*[^A-Za-z0-9])?[ADPSECF]?(?:GND|GROUND|EARTH)(?:_\d+)?$/i;
	function isGroundSymbol(globalSymbolDef) {
	    for (const [libraryName, symbolNames] of CADENCE_GROUND_SYMBOL_NAMES) {
	        if ((0, orcadLibraryProvenance_1.isFromCadenceLibrary)(globalSymbolDef.SourceLibName, libraryName)) {
	            return symbolNames.has(globalSymbolDef.Name.toUpperCase());
	        }
	    }
	    return PROJECT_GROUND_SYMBOL_PATTERN.test(globalSymbolDef.Name);
	}
	function symbolPinRef(pin) {
	    return Number.isFinite(pin.Position) ? pin.Position.toString() : pin.Name;
	}
	class OrcadDsnTransformGlobalSymbolGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformSchematic_1.TransformSchematic];
	        this.globalSymbolNum = 1;
	    }
	    *generate(parent, _rootContext) {
	        const page = parent.hierarchyEntry.page;
	        const instance = parent.instance;
	        for (const globalSymbol of page.Global ?? []) {
	            yield* this.generateFromGlobalSymbol(parent, globalSymbol, instance);
	        }
	    }
	    *generateFromGlobalSymbol(parent, globalSymbol, instance) {
	        const design = parent.hierarchyEntry.design;
	        const globalSymbolResolution = this.findGlobalSymbol(design.Cache?.GlobalSymbol, globalSymbol);
	        const globalSymbolDef = globalSymbolResolution.symbol;
	        if (globalSymbolResolution.ambiguous) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'symbol-variant-ambiguous',
	                message: `[symbol-variant-ambiguous] global symbol="${globalSymbol.SymbolName}" library="${globalSymbol.LibName ?? ''}" does not select exactly one sourced cache entry; withheld`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            return;
	        }
	        if (!globalSymbolDef) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-global-symbol-missing',
	                message: `Could not find global symbol definition for "${globalSymbol.SymbolName}"`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            return;
	        }
	        const component = new TransformComponent_1.TransformComponent(parent, {
	            baseComponent: {
	                name: globalSymbol.SymbolName,
	                isVirtual: true,
	                metadata: {
	                    'orcad.globalSymbol.name': globalSymbol.Name,
	                    'orcad.globalSymbol.sourceLibName': globalSymbolDef.SourceLibName,
	                },
	            },
	        });
	        yield component;
	        const bboxCenter = [
	            (globalSymbolDef.BoundingBox.X1 + globalSymbolDef.BoundingBox.X2) / 2,
	            (globalSymbolDef.BoundingBox.Y1 + globalSymbolDef.BoundingBox.Y2) / 2,
	        ];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
	        const pins = (globalSymbolDef.PinScalar ?? []).map((p) => ({
	            type: 'pin',
	            ref: symbolPinRef(p),
	            name: p.Name,
	            number: symbolPinRef(p),
	            start: pointTransform([p.StartX, p.StartY]),
	            end: pointTransform([p.HotptX, p.HotptY]),
	            layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
	            width: 1,
	            drawings: [],
	            metadata: { 'orcad.pin.position': p.Position },
	        }));
	        const drawings = orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({
	            pointTransform,
	        })(globalSymbolDef).map((d) => ({
	            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
	            drawing: d,
	        }));
	        const bitmapResult = orcadShapeConverters_1.OrCadShapeConvert.elementBitmaps({ pointTransform })(globalSymbolDef);
	        drawings.push(...bitmapResult.drawings.map((d) => ({
	            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Image].id],
	            drawing: d,
	        })));
	        for (const ie of bitmapResult.imageExtractions) {
	            yield new TransformImageExtraction_1.TransformImageExtraction(parent, ie.ref, ie.name, ie.data, ie.mimeType);
	        }
	        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(component, {
	            ref: globalSymbol.SymbolName,
	            name: globalSymbol.SymbolName,
	            pins,
	            drawings,
	            metadata: { 'orcad.symbol.boundingbox': globalSymbolDef.BoundingBox },
	        });
	        const connectionPin = pins[0];
	        if (connectionPin) {
	            symbol.setPinSignal(connectionPin, {
	                id: connectionPin.name,
	            });
	        }
	        yield symbol;
	        const center = OrcadDsnUtils_1.OrcadDsnUtils.computeModuleCenter(globalSymbol, globalSymbolDef.BoundingBox);
	        const moduleRef = `${instance.pathUniqueId}::${globalSymbol.DbId}`;
	        const isMultiInstance = instance.totalSheetInstances > 1;
	        const moduleProps = {
	            ref: moduleRef,
	            footprintRef: globalSymbol.SymbolName,
	            center,
	            transform: {
	                pos: [0, 0],
	                angle: globalSymbol.Rotation * 90,
	                mirrored: globalSymbol.Mirror === 1 ?
	                    [
	                        globalSymbol.Rotation === 1 || globalSymbol.Rotation === 3,
	                        globalSymbol.Rotation === 2 || globalSymbol.Rotation === 0,
	                    ]
	                    : [false, false],
	            },
	            name: `#PWR${(this.globalSymbolNum++).toString().padStart(3, '0')}`,
	            drawProperties: {},
	            metadata: {
	                Value: globalSymbol.Name,
	                'orcad.globalSymbol.name': globalSymbol.Name,
	                'orcad.globalSymbol.symbolName': globalSymbol.SymbolName,
	            },
	            multiInstance: isMultiInstance ?
	                {
	                    instanceId: parent.designId,
	                    displayModuleRef: globalSymbol.DbId.toString(),
	                }
	                : undefined,
	        };
	        if (globalSymbol.DisplayProperty) {
	            const dp = globalSymbol.DisplayProperty;
	            moduleProps.drawProperties[dp.Name] = {
	                ...(0, ModelText_1.encodeStrippedText)({
	                    raw: dp.Name === 'Name' ? globalSymbol.Name : moduleProps.name,
	                    dialects: [ModelText_1.TextDialect.Orcad],
	                }),
	                pos: [dp.LocX + globalSymbol.LocX, dp.LocY + globalSymbol.LocY],
	                layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.ValueLabel].id,
	                angle: dp.Rotation * 90,
	                justify: [-1, -1],
	                size: Math.abs(dp.Font.Height / 2),
	                thickness: 1,
	                mirror: [false, false],
	                hidden: false,
	            };
	        }
	        let model;
	        if (isGroundSymbol(globalSymbolDef)) {
	            model = {
	                modelId: 'ground.standard',
	            };
	        }
	        else {
	            model = {
	                modelId: 'supply.standard',
	            };
	            try {
	                const value = (0, sigma_engine_shared_1.parseNumericalSIValue)(globalSymbol.Name, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions['voltage']);
	                model.inferenceReport = {
	                    parameters: {
	                        voltage: {
	                            source: 'module-value',
	                            value,
	                        },
	                    },
	                };
	            }
	            catch {
	            }
	        }
	        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
	        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
	            name: moduleProps.name,
	            component,
	            model,
	            value: globalSymbol.Name,
	        });
	        yield projectModule;
	        const transformModule = new TransformModule_1.TransformModule(parent, moduleProps, component, projectModule);
	        yield transformModule;
	        if (isMultiInstance && instance.instanceIndex === 0) {
	            const multiInstanceParent = parent.parent;
	            if (multiInstanceParent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
	                const displayModuleProps = {
	                    ...moduleProps,
	                    multiInstance: undefined,
	                };
	                yield new TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule(multiInstanceParent, displayModuleProps, component);
	            }
	        }
	        if (pins.length === 0) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-global-symbol-no-pins',
	                message: `Global symbol "${globalSymbol.Name}" has no pins. No connections will be created.`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            return;
	        }
	        if (pins.length > 1) {
	            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
	                type: 'orcad-dsn-global-symbol-multiple-pins',
	                message: `Global symbol "${globalSymbol.Name}" has ${pins.length} pins. Only the first pin will be connected.`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        const pin = globalSymbolDef.PinScalar?.[0];
	        if (pin) {
	            const pinPosition = OrcadDsnUtils_1.OrcadDsnUtils.transformPinToSchematicCoords({ HotptX: pin.HotptX, HotptY: pin.HotptY }, globalSymbol, globalSymbolDef.BoundingBox);
	            yield new TransformModulePinConnection_1.TransformModulePinConnection(parent, {
	                position: pinPosition,
	                connection: {
	                    moduleRef,
	                    moduleName: moduleProps.name,
	                    pinRef: pins[0].ref,
	                    appliesNetName: {
	                        label: globalSymbol.Name,
	                        labelType: 'global',
	                        strategyMetadata: {
	                            'net-properties': (globalSymbol.UserProperty ?? []).map((property) => ({
	                                name: property.Name,
	                                value: property.Value,
	                            })),
	                        },
	                    },
	                },
	            });
	        }
	    }
	    findGlobalSymbol(symbols, globalSymbol) {
	        if (!symbols) {
	            return { symbol: undefined, ambiguous: false };
	        }
	        return (0, OrcadDsnSymbolUtils_1.resolveSymbolVariant)(symbols, globalSymbol.SymbolName, globalSymbol.LibName);
	    }
	}
	OrcadDsnTransformGlobalSymbolGenerator.OrcadDsnTransformGlobalSymbolGenerator = OrcadDsnTransformGlobalSymbolGenerator$1;
	
	return OrcadDsnTransformGlobalSymbolGenerator;
}

var OrcadNetProperties = {};

var hasRequiredOrcadNetProperties;

function requireOrcadNetProperties () {
	if (hasRequiredOrcadNetProperties) return OrcadNetProperties;
	hasRequiredOrcadNetProperties = 1;
	Object.defineProperty(OrcadNetProperties, "__esModule", { value: true });
	OrcadNetProperties.emitOrcadNetProperties = emitOrcadNetProperties;
	function emitOrcadNetProperties(project, groups, report) {
	    for (const group of groups) {
	        const values = new Map();
	        const connections = new Map();
	        for (const nets of group.getNets().values()) {
	            for (const net of nets) {
	                const properties = [
	                    ...net.wires.flatMap((wire) => wire.strategyMetadata?.['net-properties'] ?? []),
	                    ...net.connections.flatMap((entry) => entry.connection.appliesNetName?.strategyMetadata?.['net-properties'] ?? []),
	                ];
	                for (const { name, value } of properties) {
	                    const entries = values.get(name) ?? new Set();
	                    entries.add(value);
	                    values.set(name, entries);
	                }
	                for (const { connection } of net.connections) {
	                    const pins = connections.get(connection.moduleRef) ?? new Set();
	                    pins.add(connection.pinRef);
	                    connections.set(connection.moduleRef, pins);
	                }
	            }
	        }
	        if (values.size === 0) {
	            continue;
	        }
	        const properties = {};
	        for (const [name, entries] of values) {
	            const [value] = entries;
	            if (entries.size !== 1 || value === undefined) {
	                report(`[net-property-conflict] net=${JSON.stringify(group.physicalName)} property=${JSON.stringify(name)} values=${JSON.stringify([...entries])}`);
	            }
	            else {
	                properties[name] = value;
	            }
	        }
	        const targets = [];
	        for (const net of Object.values(project.designs).flatMap((sheet) => Object.values(sheet.nets))) {
	            if (net.name !== group.physicalName ||
	                !net.connections.some((connection) => connections.get(connection.moduleRef)?.has(connection.pinRef))) {
	                continue;
	            }
	            targets.push(net);
	        }
	        const global = group.physicalName ? project.globalNets[group.physicalName] : undefined;
	        if (targets.length > 0 && global) {
	            targets.push(global);
	        }
	        for (const net of targets) {
	            for (const [name, value] of Object.entries(properties)) {
	                const existing = net.metadata?.[name];
	                if (existing !== undefined && existing !== value) {
	                    report(`[net-property-conflict] net=${JSON.stringify(net.name)} property=${JSON.stringify(name)} existing=${JSON.stringify(existing)} recorded=${JSON.stringify(value)}`);
	                    continue;
	                }
	                net.metadata = { ...net.metadata, [name]: value };
	            }
	        }
	    }
	}
	
	return OrcadNetProperties;
}

var hasRequiredOrcadDsnOrchestratedTransformer;

function requireOrcadDsnOrchestratedTransformer () {
	if (hasRequiredOrcadDsnOrchestratedTransformer) return OrcadDsnOrchestratedTransformer;
	hasRequiredOrcadDsnOrchestratedTransformer = 1;
	Object.defineProperty(OrcadDsnOrchestratedTransformer, "__esModule", { value: true });
	OrcadDsnOrchestratedTransformer.__testing = void 0;
	OrcadDsnOrchestratedTransformer.transformDsnProject = transformDsnProject;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformOrchestrator_1 = requireTransformOrchestrator();
	const TransformProject_1 = requireTransformProject();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const OrcadDsnTransformProjectGenerator_1 = requireOrcadDsnTransformProjectGenerator();
	const OrcadDsnTransformSchematicGenerator_1 = requireOrcadDsnTransformSchematicGenerator();
	const OrcadDsnTransformModuleGenerator_1 = requireOrcadDsnTransformModuleGenerator();
	const OrcadDsnTransformComponentGenerator_1 = requireOrcadDsnTransformComponentGenerator();
	const OrcadDsnTransformNetWireGenerator_1 = requireOrcadDsnTransformNetWireGenerator();
	const OrcadDsnTransformPortGenerator_1 = requireOrcadDsnTransformPortGenerator();
	const OrcadDsnTransformSheetSymbolGenerator_1 = requireOrcadDsnTransformSheetSymbolGenerator();
	const OrcadDsnTransformOffPageConnectorGenerator_1 = requireOrcadDsnTransformOffPageConnectorGenerator();
	const OrcadDsnTransformGraphicsGenerator_1 = requireOrcadDsnTransformGraphicsGenerator();
	const OrcadDsnTransformGlobalSymbolGenerator_1 = requireOrcadDsnTransformGlobalSymbolGenerator();
	const OrcadNetProperties_1 = requireOrcadNetProperties();
	function transformDsnProject(parsedDef, options) {
	    const transformer = new TransformOrchestrator_1.TransformOrchestrator({
	        generators: [
	            new OrcadDsnTransformProjectGenerator_1.OrCADDsnTransformProjectGenerator(),
	            new OrcadDsnTransformSchematicGenerator_1.OrCadDsnTransformSchematicGenerator(),
	            new OrcadDsnTransformModuleGenerator_1.OrCadDsnTransformModuleGenerator(),
	            new OrcadDsnTransformNetWireGenerator_1.OrcadDsnTransformNetWireGenerator(),
	            new OrcadDsnTransformPortGenerator_1.OrcadDsnTransformPortGenerator(),
	            new OrcadDsnTransformSheetSymbolGenerator_1.OrcadDsnTransformSheetSymbolGenerator(),
	            new OrcadDsnTransformOffPageConnectorGenerator_1.OrcadDsnTransformOffPageConnectorGenerator(),
	            new OrcadDsnTransformGraphicsGenerator_1.OrcadDsnTransformGraphicsGenerator(),
	            new OrcadDsnTransformGlobalSymbolGenerator_1.OrcadDsnTransformGlobalSymbolGenerator(),
	            new OrcadDsnTransformComponentGenerator_1.OrCadDsnTransformComponentGenerator(),
	        ],
	    });
	    const rootContext = {
	        parsedDef,
	        options,
	    };
	    const result = transformer.transform(rootContext, TransformProject_1.TransformProject);
	    for (const project of transformer.getRegistry().getPrimaries(TransformProject_1.TransformProject)) {
	        (0, OrcadNetProperties_1.emitOrcadNetProperties)(project.compiled, project.resolvedNetGroups, (message) => {
	            project.compiled.processingResults.innerResults.push({
	                type: 'orcad-net-property',
	                message,
	                severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
	            });
	        });
	    }
	    for (let projectIndex = 0; projectIndex < result.length; projectIndex++) {
	        const project = result[projectIndex];
	        for (const message of rootContext.hierarchyPortDiagnosticSets?.[projectIndex] ?? []) {
	            project.processingResults.innerResults.push({
	                type: 'orcad-dsn-transformer',
	                message,
	                severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
	            });
	        }
	        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
	    }
	    if (rootContext.designHierarchyResults) {
	        for (let i = 0; i < result.length && i < rootContext.designHierarchyResults.length; i++) {
	            result[i].designHierarchy = rootContext.designHierarchyResults[i];
	        }
	    }
	    const imageExtractionObjects = transformer.getRegistry().getPrimaries(TransformImageExtraction_1.TransformImageExtraction);
	    const extractedImages = imageExtractionObjects.map((obj) => obj.compiled);
	    const { detectedRootSourceFiles } = rootContext;
	    if (detectedRootSourceFiles === undefined) {
	        throw new Error('OrCAD transform finished without recording the detected root source files');
	    }
	    return {
	        projectModels: result,
	        extractedImages,
	        rootSourceFiles: detectedRootSourceFiles,
	        ...collectFlatNetBindings(rootContext, transformer.getRegistry().getPrimaries(TransformProject_1.TransformProject)),
	    };
	}
	const BusDimensionName = 'bus';
	function busPositionKey(position) {
	    return `${position[0].toFixed(4)},${position[1].toFixed(4)}`;
	}
	function isComponentPinConnection(connection) {
	    return connection.appliesNetName?.labelType !== 'global';
	}
	const ForcedUniquenessSuffixPattern = /_\d+$/;
	function labelSpellsResolvedName(label, resolvedName) {
	    const normalizedLabel = label.toUpperCase();
	    const normalizedResolved = resolvedName.toUpperCase();
	    if (normalizedLabel === normalizedResolved) {
	        return true;
	    }
	    const beforeTieBreak = normalizedResolved.replace(ForcedUniquenessSuffixPattern, '');
	    return beforeTieBreak !== normalizedResolved && beforeTieBreak === normalizedLabel;
	}
	function netNameConnectsDesignWide(net, resolvedName) {
	    if (net.labels.some((label) => label.labelType === 'global' &&
	        (labelSpellsResolvedName(label.formattedLabel ?? label.originalLabel, resolvedName) ||
	            labelSpellsResolvedName(label.originalLabel, resolvedName)))) {
	        return true;
	    }
	    return net.connections.some(({ connection }) => {
	        const applies = connection.appliesNetName;
	        if (!applies || !labelSpellsResolvedName(applies.label, resolvedName)) {
	            return false;
	        }
	        return applies.labelType === 'global' || applies.labelType === 'power-pin';
	    });
	}
	class FlatNetUnion {
	    constructor() {
	        this.parents = [];
	    }
	    add() {
	        this.parents.push(this.parents.length);
	        return this.parents.length - 1;
	    }
	    find(id) {
	        let root = id;
	        while (this.parents[root] !== root) {
	            root = this.parents[root] ?? root;
	        }
	        let walk = id;
	        while (this.parents[walk] !== root) {
	            const next = this.parents[walk] ?? root;
	            this.parents[walk] = root;
	            walk = next;
	        }
	        return root;
	    }
	    union(left, right) {
	        const leftRoot = this.find(left);
	        const rightRoot = this.find(right);
	        if (leftRoot !== rightRoot) {
	            this.parents[leftRoot] = rightRoot;
	        }
	    }
	}
	function registerFlatNetName(candidates, design, netName, id, connects) {
	    if (!connects) {
	        return;
	    }
	    const byName = candidates.byDesignAndName.get(design) ?? new Map();
	    const entry = byName.get(netName) ?? { connectingCandidateIds: [] };
	    entry.connectingCandidateIds.push(id);
	    byName.set(netName, entry);
	    candidates.byDesignAndName.set(design, byName);
	}
	function flatNetCandidates(projects) {
	    const candidates = {
	        union: new FlatNetUnion(),
	        ids: new Map(),
	        byDesignAndName: new Map(),
	        unresolved: new Map(),
	    };
	    for (const project of projects) {
	        for (const group of project.resolvedNetGroups) {
	            const id = candidates.union.add();
	            const name = group.physicalName;
	            if (!name) {
	                candidates.unresolved.set(id, 'the naming rules resolved no physical name for the group it belongs to');
	            }
	            for (const [instance, nets] of group.getNets()) {
	                const design = instance.hierarchyEntry?.design;
	                for (const net of nets) {
	                    candidates.ids.set(net, id);
	                    if (name && design) {
	                        registerFlatNetName(candidates, design, name, id, netNameConnectsDesignWide(net, name));
	                    }
	                }
	            }
	        }
	    }
	    return candidates;
	}
	function collectFlatNetBindings(rootContext, projects) {
	    const candidates = flatNetCandidates(projects);
	    const busMembers = [];
	    const pageNetBindings = [];
	    const portNetBindings = [];
	    const offPageConnectorNetBindings = [];
	    const uncompiledNetObjects = [];
	    const pending = [];
	    for (const record of rootContext.pageSchematicRecords ?? []) {
	        if (!record.schematic.isCompiled) {
	            uncompiledNetObjects.push({ page: record.page });
	            continue;
	        }
	        const instance = record.schematic.instance;
	        const parent = instance?.path[instance.path.length - 1];
	        const design = record.schematic.hierarchyEntry.design;
	        const entry = {
	            occurrence: {
	                page: record.page,
	                designId: record.schematic.compiled.designId,
	                occurrencePath: instance?.pathUniqueId ?? '',
	                ...(parent === undefined ? {} : { parentOccurrencePath: parent.pathUniqueId }),
	                ...(instance?.sheetSymbol === undefined ? {} : { sheetSymbolDbId: instance.sheetSymbol.DbId }),
	                ...(instance?.instOccurence === undefined ? {} : { occurrence: instance.instOccurence }),
	            },
	            atComponentPins: [],
	            all: [],
	            ports: new Map(),
	            connectors: new Map(),
	        };
	        pending.push(entry);
	        const memberNetsAtPosition = new Map();
	        const busNetsAtPosition = new Map();
	        const memberPositions = new Map();
	        for (const net of record.schematic.netCombobulator.iterateNets()) {
	            let flatNetId = candidates.ids.get(net);
	            if (flatNetId === undefined) {
	                flatNetId = candidates.union.add();
	                candidates.ids.set(net, flatNetId);
	                const netName = String(net.name);
	                registerFlatNetName(candidates, design, netName, flatNetId, netNameConnectsDesignWide(net, netName));
	                if (net.labels.some((label) => label.labelType === 'hierarchical-port' || label.labelType === 'off-page-connector')) {
	                    candidates.unresolved.set(flatNetId, `no grouping rule claimed the net "${String(net.name)}" a connector sits on`);
	                }
	            }
	            entry.all.push(flatNetId);
	            if (net.connections.some(({ connection }) => isComponentPinConnection(connection))) {
	                entry.atComponentPins.push(flatNetId);
	            }
	            for (const object of net.netObjects) {
	                for (const endpoint of object.endpoints) {
	                    const key = busPositionKey(endpoint.position);
	                    if (endpoint.dimension === BusDimensionName) {
	                        const busIds = busNetsAtPosition.get(key) ?? [];
	                        busIds.push(flatNetId);
	                        busNetsAtPosition.set(key, busIds);
	                        continue;
	                    }
	                    const flatNetIds = memberNetsAtPosition.get(key) ?? [];
	                    flatNetIds.push(flatNetId);
	                    memberNetsAtPosition.set(key, flatNetIds);
	                    const member = memberPositions.get(flatNetId) ?? { memberName: String(net.name), positions: [] };
	                    member.positions.push(key);
	                    memberPositions.set(flatNetId, member);
	                }
	            }
	        }
	        for (const [candidateId, member] of memberPositions) {
	            for (const position of member.positions) {
	                for (const busCandidateId of busNetsAtPosition.get(position) ?? []) {
	                    busMembers.push({ busCandidateId, memberName: member.memberName, candidateId });
	                }
	            }
	        }
	        for (const net of record.schematic.netCombobulator.iterateNets()) {
	            const flatNetId = candidates.ids.get(net);
	            if (flatNetId === undefined) {
	                continue;
	            }
	            const objectFlatNetIds = [flatNetId];
	            for (const object of net.netObjects) {
	                for (const endpoint of object.endpoints) {
	                    if (endpoint.dimension !== BusDimensionName) {
	                        continue;
	                    }
	                    for (const memberFlatNetId of memberNetsAtPosition.get(busPositionKey(endpoint.position)) ?? []) {
	                        objectFlatNetIds.push(memberFlatNetId);
	                    }
	                }
	            }
	            for (const label of net.labels) {
	                const portDbId = label.strategyMetadata?.['hierarchical-port']?.portDbId;
	                if (portDbId !== undefined) {
	                    const ids = entry.ports.get(Number(portDbId)) ?? [];
	                    for (const objectFlatNetId of objectFlatNetIds) {
	                        ids.push(objectFlatNetId);
	                    }
	                    entry.ports.set(Number(portDbId), ids);
	                }
	                const connectorDbId = label.strategyMetadata?.['off-page-connector']?.connectorDbId;
	                if (connectorDbId !== undefined) {
	                    const ids = entry.connectors.get(Number(connectorDbId)) ?? [];
	                    for (const objectFlatNetId of objectFlatNetIds) {
	                        ids.push(objectFlatNetId);
	                    }
	                    entry.connectors.set(Number(connectorDbId), ids);
	                }
	            }
	        }
	    }
	    for (const byName of candidates.byDesignAndName.values()) {
	        for (const entry of byName.values()) {
	            for (const id of entry.connectingCandidateIds.slice(1)) {
	                candidates.union.union(entry.connectingCandidateIds[0], id);
	            }
	        }
	    }
	    const membersByBusAndName = new Map();
	    for (const member of busMembers) {
	        const key = JSON.stringify([candidates.union.find(member.busCandidateId), member.memberName]);
	        const ids = membersByBusAndName.get(key) ?? [];
	        ids.push(member.candidateId);
	        membersByBusAndName.set(key, ids);
	    }
	    for (const ids of membersByBusAndName.values()) {
	        for (const id of ids.slice(1)) {
	            candidates.union.union(ids[0], id);
	        }
	    }
	    for (const record of rootContext.portNetLabelRecords ?? []) {
	        if (!record.label.isCompiled) {
	            uncompiledNetObjects.push({ page: record.page, portDbId: record.portDbId });
	        }
	    }
	    const resolve = (ids) => new Set(ids.map((id) => candidates.union.find(id)));
	    for (const entry of pending) {
	        pageNetBindings.push({
	            ...entry.occurrence,
	            flatNetIdsAtComponentPins: resolve(entry.atComponentPins),
	            flatNetIds: resolve(entry.all),
	        });
	        for (const [portDbId, ids] of entry.ports) {
	            portNetBindings.push({ ...entry.occurrence, portDbId, flatNetIds: resolve(ids) });
	        }
	        for (const [connectorDbId, ids] of entry.connectors) {
	            offPageConnectorNetBindings.push({ ...entry.occurrence, connectorDbId, flatNetIds: resolve(ids) });
	        }
	    }
	    const unresolvedFlatNets = new Map();
	    for (const [id, reason] of candidates.unresolved) {
	        unresolvedFlatNets.set(candidates.union.find(id), reason);
	    }
	    return {
	        portNetBindings,
	        offPageConnectorNetBindings,
	        pageNetBindings,
	        uncompiledNetObjects,
	        unresolvedFlatNets: Array.from(unresolvedFlatNets, ([flatNetId, reason]) => ({ flatNetId, reason })),
	    };
	}
	OrcadDsnOrchestratedTransformer.__testing = { netNameConnectsDesignWide };
	
	return OrcadDsnOrchestratedTransformer;
}

var orcadDsnTransformer = {};

var addMetadataWithoutDuplicates = {};

var hasRequiredAddMetadataWithoutDuplicates;

function requireAddMetadataWithoutDuplicates () {
	if (hasRequiredAddMetadataWithoutDuplicates) return addMetadataWithoutDuplicates;
	hasRequiredAddMetadataWithoutDuplicates = 1;
	Object.defineProperty(addMetadataWithoutDuplicates, "__esModule", { value: true });
	addMetadataWithoutDuplicates.addMetadataWithoutDuplicates = addMetadataWithoutDuplicates$1;
	function addMetadataWithoutDuplicates$1(a, b) {
	    a = { ...(a ?? {}) };
	    for (const [key, value] of Object.entries(b ?? {})) {
	        if (a[key] === undefined) {
	            a[key] = value;
	        }
	        else if (a[key] !== value) {
	            for (let i = 0; i < 10; i++) {
	                const newKey = `${key}${i + 1}`;
	                if (a[newKey] === undefined) {
	                    a[newKey] = value;
	                    break;
	                }
	            }
	        }
	    }
	    return a;
	}
	
	return addMetadataWithoutDuplicates;
}

var hasRequiredOrcadDsnTransformer;

function requireOrcadDsnTransformer () {
	if (hasRequiredOrcadDsnTransformer) return orcadDsnTransformer;
	hasRequiredOrcadDsnTransformer = 1;
	Object.defineProperty(orcadDsnTransformer, "__esModule", { value: true });
	orcadDsnTransformer.OrCadDSNTransformer = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const Transform_1 = requireTransform();
	const alphaDesignatorToConstant_1 = requireAlphaDesignatorToConstant();
	const addMetadataWithoutDuplicates_1 = requireAddMetadataWithoutDuplicates();
	const orcadLayers_1 = requireOrcadLayers();
	const Font_1 = requireFont();
	const fonts_1 = requireFonts();
	const orcadShapeConverters_1 = requireOrcadShapeConverters();
	const Area_1 = requireArea();
	const orcadDsnPhysicalPartFallback_1 = requireOrcadDsnPhysicalPartFallback();
	class OrCadDSNTransformer {
	    static { this.DEFAULT_WIRE_WIDTH = 0.3; }
	    static { this.WIRE_WIDTH_MULTIPLIER = 0.1; }
	    constructor(schemas) {
	        this.globalSymbolNum = 1;
	        this.offPageSymbols = {};
	        this.portSymbols = {};
	        this.schematicHierarchies = [];
	        this.logicalSchemas = new Map(schemas.map((s) => [s.Name, s]));
	        this.project = {
	            type: 'orcad',
	            layerSets: {},
	            designs: {},
	            components: {},
	            modules: [],
	            warnings: [],
	            processingResults: {
	                type: 'project-processing',
	                message: 'Project processed successfully',
	                innerResults: [],
	            },
	            fonts: {
	                fonts: {},
	            },
	            globalNets: {},
	        };
	    }
	    transformToProjectModel(netlist) {
	        for (const [, design] of this.logicalSchemas) {
	            if (design.Cache) {
	                this.extractComponentsFromCache(design.Cache);
	            }
	            let sheetIndex = 0;
	            for (const schematic of design.Schematic) {
	                this.extractSchematicFromSchematic(design, schematic, sheetIndex++);
	            }
	        }
	        if (netlist) {
	            this.applyNetlist(netlist);
	        }
	        else {
	            this.logProcessResult({
	                type: 'netlist-missing',
	                message: 'No netlist provided. Module pin nets will not be populated.',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            });
	        }
	        (0, Font_1.packModelFonts)(this.project);
	        return this.project;
	    }
	    logProcessResult(result) {
	        this.project.processingResults.innerResults.push(result);
	    }
	    extractComponentsFromCache(cache) {
	        for (const pkg of cache.Package ?? []) {
	            for (const libPart of pkg.LibPart) {
	                this.extractComponentFromLibPart(pkg, libPart);
	            }
	        }
	        for (const globalSymbol of cache.GlobalSymbol ?? []) {
	            this.extractComponentFromGlobalSymbol(globalSymbol);
	        }
	        for (const portSymbol of cache.PortSymbol ?? []) {
	            this.extractPortSymbol(portSymbol);
	        }
	        for (const offPageSymbol of cache.OffPageSymbol ?? []) {
	            this.extractOffPageSymbol(offPageSymbol);
	        }
	    }
	    extractComponentFromGlobalSymbol(globalSymbol) {
	        const bboxCenter = [
	            (globalSymbol.BoundingBox.X1 + globalSymbol.BoundingBox.X2) / 2,
	            (globalSymbol.BoundingBox.Y1 + globalSymbol.BoundingBox.Y2) / 2,
	        ];
	        const pointTransform = (p) => {
	            return (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
	        };
	        const componentSymbol = {
	            ref: globalSymbol.Name,
	            name: globalSymbol.Name,
	            drawings: orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(globalSymbol).map((d) => {
	                return {
	                    layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
	                    drawing: d,
	                };
	            }),
	            pins: globalSymbol.PinScalar?.map((p) => {
	                return {
	                    type: 'pin',
	                    ref: p.Position.toString() || p.Name,
	                    name: p.Name,
	                    number: p.Position.toString() || p.Name,
	                    start: pointTransform([p.StartX, p.StartY]),
	                    end: pointTransform([p.HotptX, p.HotptY]),
	                    layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
	                    drawings: [],
	                    metadata: {
	                        ['orcad.pin.position']: p.Position,
	                    },
	                };
	            }),
	            metadata: {
	                'orcad.symbol.boundingbox': globalSymbol.BoundingBox,
	            },
	        };
	        const component = {
	            name: globalSymbol.Name,
	            numPins: componentSymbol.pins.length,
	            numPads: 0,
	            footprints: [],
	            symbols: [componentSymbol],
	            signals: componentSymbol.pins.map((p) => ({ id: p.ref, name: p.name, pinRefs: [p.ref], padRefs: [] })),
	            isVirtual: true,
	            metadata: {
	                'orcad.globalSymbol.name': globalSymbol.Name,
	                'orcad.globalSymbol.sourceLibName': globalSymbol.SourceLibName,
	            },
	        };
	        this.project.components[component.name] = component;
	    }
	    extractComponentFromLibPart(pkg, libPart) {
	        const componentRef = `${pkg.SourceLibName}::${pkg.Name}`;
	        let component = this.project.components[componentRef];
	        if (component) {
	            if (!component.metadata) {
	                component.metadata = {};
	            }
	            component.metadata['orcad.package.sectionCount'] =
	                (component.metadata['orcad.package.sectionCount'] ?? 0) + 1;
	        }
	        else {
	            component = {
	                name: componentRef,
	                numPins: 0,
	                numPads: 0,
	                footprints: [],
	                symbols: [],
	                signals: [],
	                metadata: {
	                    'orcad.package.alphabeticNumbering': pkg.AlphabeticNumbering,
	                    'orcad.package.isHomogeneous': pkg.IsHomogeneous,
	                    'orcad.package.name': pkg.Name,
	                    'orcad.package.pcbFootprint': pkg.PcbFootprint,
	                    'orcad.package.pcbLib': pkg.PcbLib,
	                    'orcad.package.refdesPrefix': pkg.RefdesPrefix,
	                    'orcad.package.sourceLibName': pkg.SourceLibName,
	                },
	            };
	        }
	        if (libPart.NormalView && libPart.NormalView.length > 1) {
	            throw new Error(`Not sure what to do with multiple normal views for component ${component.name}`);
	        }
	        const physicalParts = libPart.PhysicalPart?.length ? libPart.PhysicalPart : [(0, orcadDsnPhysicalPartFallback_1.synthesizePhysicalPartFromPins)(libPart)];
	        if (!libPart.PhysicalPart?.length) {
	            this.logProcessResult({
	                type: 'orcad-dsn-physical-part-missing',
	                message: `Synthesized a physical part for ${pkg.SourceLibName}::${pkg.Name}::${libPart.CellName}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        for (const physicalPart of physicalParts) {
	            const normalView = libPart.NormalView?.[0];
	            const normalViewPinCount = (normalView?.PinScalar?.length ?? 0) + (normalView?.PinBus?.length ?? 0);
	            if (physicalPart.PinNumber && physicalPart.PinNumber.length !== normalViewPinCount) {
	                throw new Error(`PhysicalPart.PinNumber length does not match NormalView pin count for ${component.name}::${libPart.CellName}`);
	            }
	        }
	        for (const physicalPart of physicalParts) {
	            const physicalPartIndex = physicalParts.indexOf(physicalPart);
	            let physicalPartSuffix = '';
	            if (physicalParts.length > 1) {
	                physicalPartSuffix = `-${physicalPartIndex + 1}`;
	            }
	            const pinPositionMap = new Map();
	            for (const pinNumber of physicalPart.PinNumber ?? []) {
	                if (pinPositionMap.has(pinNumber.Position)) {
	                    throw new Error(`Duplicate pin position ${pinNumber.Position} found in physical part for ${component.name}::${libPart.CellName}`);
	                }
	                pinPositionMap.set(pinNumber.Position, pinNumber.Number.toString());
	            }
	            for (const normalView of libPart.NormalView ?? []) {
	                const viewSignalNames = new Set();
	                const ref = `${libPart.CellName}${normalView.Suffix}${physicalPartSuffix}`;
	                const metadata = {
	                    'orcad.symbol.boundingbox': normalView.BoundingBox,
	                    'orcad.symbol.sectionIndex': component.metadata?.['orcad.package.sectionCount'] ?? 0,
	                };
	                if (physicalPartSuffix) {
	                    metadata['orcad.symbol.physicalPartIndex'] = physicalPartIndex;
	                }
	                const bboxCenter = [
	                    (normalView.BoundingBox.X1 + normalView.BoundingBox.X2) / 2,
	                    (normalView.BoundingBox.Y1 + normalView.BoundingBox.Y2) / 2,
	                ];
	                const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
	                const symbol = {
	                    ref,
	                    name: ref,
	                    drawings: [
	                        ...orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(normalView).map((drawing) => {
	                            return {
	                                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
	                                drawing,
	                            };
	                        }),
	                        ...(normalView.CommentText ?? []).map((c) => ({
	                            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
	                            drawing: {
	                                type: 'text',
	                                value: c.Name,
	                                pos: pointTransform([c.LocX, c.LocY]),
	                                angle: c.Rotation ?? 0,
	                                justify: [-1, 0],
	                                size: this.scaleFont(c.Font),
	                                thickness: 1,
	                            },
	                        })),
	                    ],
	                    pins: [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])].flatMap((p) => {
	                        let pinRef = pinPositionMap.get(p.Position);
	                        if (pinRef === undefined) {
	                            throw new Error(`Pin position ${p.Position} not found in pin position map`);
	                        }
	                        if (pinRef === '') {
	                            pinRef = p.Name;
	                        }
	                        if (viewSignalNames.has(p.Name)) {
	                            throw new Error(`Duplicate signal name ${p.Name} found in normal view ${normalView.Suffix}`);
	                        }
	                        let signal = component.signals.find((s) => s.name === p.Name);
	                        if (!signal) {
	                            signal = {
	                                id: p.Name,
	                                name: p.Name,
	                                pinRefs: [pinRef],
	                                padRefs: [],
	                            };
	                            component.signals.push(signal);
	                        }
	                        else {
	                            signal.pinRefs.push(pinRef);
	                        }
	                        const metadata = {
	                            ['orcad.pin.position']: p.Position,
	                        };
	                        if (p.IsNoConnect) {
	                            metadata['orcad.pin.isNoConnect'] = true;
	                        }
	                        return {
	                            type: 'pin',
	                            ref: pinRef,
	                            name: p.Name,
	                            number: pinRef,
	                            start: pointTransform([p.StartX, p.StartY]),
	                            end: pointTransform([p.HotptX, p.HotptY]),
	                            layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
	                            drawings: this.generatePinText(p, pinRef, pointTransform),
	                            metadata,
	                        };
	                    }),
	                    metadata,
	                };
	                component.symbols.push(symbol);
	            }
	        }
	        component.numPins = component.symbols.reduce((p, c) => p + c.pins.length, 0);
	        this.project.components[component.name] = component;
	    }
	    generatePinText(p, pinRef, pointTransform) {
	        let nameJustify;
	        let pinRefJustify;
	        let angle;
	        if (p.StartX > p.HotptX) {
	            nameJustify = [-1, 0];
	            pinRefJustify = [1, 0];
	            angle = 0;
	        }
	        else if (p.StartX < p.HotptX) {
	            nameJustify = [1, 0];
	            pinRefJustify = [-1, 0];
	            angle = 0;
	        }
	        else {
	            if (p.StartY > p.HotptY) {
	                nameJustify = [1, 0];
	                pinRefJustify = [-1, 0];
	                angle = 90;
	            }
	            else {
	                nameJustify = [-1, 0];
	                pinRefJustify = [1, 0];
	                angle = 90;
	            }
	        }
	        const textSize = 4;
	        const estNameTextWidth = p.Name.length * textSize;
	        const nameTextOffset = estNameTextWidth / 2;
	        const estPinRefTextWidth = pinRef.length * textSize;
	        const pinRefTextOffset = estPinRefTextWidth / 2;
	        let nameOffset = [0, 0];
	        let pinRefOffset = [0, 0];
	        switch (angle) {
	            case 0:
	                nameOffset = [nameTextOffset * -nameJustify[0], 0];
	                pinRefOffset = [pinRefTextOffset * -pinRefJustify[0], 0];
	                break;
	            case 90:
	                nameOffset = [0, nameTextOffset * nameJustify[0]];
	                pinRefOffset = [0, pinRefTextOffset * pinRefJustify[0]];
	                break;
	        }
	        pointTransform = pointTransform ?? ((p) => p);
	        return [
	            {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinName].id],
	                drawing: {
	                    type: 'text',
	                    value: p.Name,
	                    pos: (0, Transform_1.translatePoint)(pointTransform([p.StartX, p.StartY]), nameOffset),
	                    angle,
	                    justify: [0, 0],
	                    size: textSize,
	                    thickness: 1,
	                },
	            },
	            {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinNumber].id],
	                drawing: {
	                    type: 'text',
	                    value: pinRef,
	                    pos: (0, Transform_1.translatePoint)(pointTransform([p.HotptX, p.HotptY]), pinRefOffset),
	                    angle,
	                    justify: [0, 0],
	                    size: textSize,
	                    thickness: 1,
	                },
	            },
	        ];
	    }
	    extractSchematicFromSchematic(design, schematic, sheetIndex) {
	        for (const page of schematic.Page ?? []) {
	            this.extractSchematicPage(schematic, page, design, sheetIndex);
	        }
	    }
	    extractSchematicPage(schematic, page, design, sheetIndex) {
	        const foundPageNumber = page.TitleBlock?.flatMap((titleBlock) => titleBlock.UserProperty ?? []).find((p) => p.Name === 'Page Number')?.Value;
	        const sch = {
	            modelType: 'schematic',
	            designId: `${schematic.Name} - ${page.Name}`,
	            sourceFile: design.Name,
	            sheetIndex: foundPageNumber ? parseInt(foundPageNumber) - 1 : sheetIndex,
	            layerSet: orcadLayers_1.ORCAD_SCH_LAYERSET,
	            drawings: {},
	            edges_bbox: {
	                minx: Number.NEGATIVE_INFINITY,
	                miny: Number.NEGATIVE_INFINITY,
	                maxx: Number.POSITIVE_INFINITY,
	                maxy: Number.POSITIVE_INFINITY,
	            },
	            initial_zoombox: {
	                minx: Number.NEGATIVE_INFINITY,
	                miny: Number.NEGATIVE_INFINITY,
	                maxx: Number.POSITIVE_INFINITY,
	                maxy: Number.POSITIVE_INFINITY,
	            },
	            font: {
	                default: fonts_1.Font.Newstroke,
	            },
	            ports: {},
	            modules: [],
	            nets: {},
	            warnings: [],
	            multiInstanceConfiguration: {
	                instances: [],
	                multiInstanceModules: [],
	            },
	        };
	        if (!this.project.layerSets[orcadLayers_1.ORCAD_SCH_LAYERSET]) {
	            this.project.layerSets[orcadLayers_1.ORCAD_SCH_LAYERSET] = orcadLayers_1.OrCadSchLayers.map((layer) => {
	                layer.defaultVisible = typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true;
	                return layer;
	            });
	        }
	        this.project.designs[sch.designId] = sch;
	        for (const partInst of page.PartInst ?? []) {
	            this.extractModuleFromPartInst(sch, partInst);
	        }
	        for (const globalSymbol of page.Global ?? []) {
	            this.extractModuleFromGlobalSymbol(sch, globalSymbol);
	        }
	        for (const netScalar of page.NetScalar ?? []) {
	            this.extractNetFromNetScalar(sch, netScalar);
	        }
	        for (const netBus of page.NetBus ?? []) {
	            this.extractNetFromNetBus(sch, netBus);
	        }
	        for (const busEntry of page.BusEntry ?? []) {
	            this.extractBusEntry(sch, busEntry);
	        }
	        for (const port of page.Port ?? []) {
	            this.extractPort(sch, port);
	        }
	        for (const offPageConnector of page.OffPageConnector ?? []) {
	            this.extractOffPageConnector(sch, offPageConnector);
	        }
	        for (const commentText of page.GraphicCommentTextInst ?? []) {
	            this.extractCommentText(sch, commentText);
	        }
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Other].id] ??= [];
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Other].id].unshift(...[
	            ...(page.GraphicLineInst ?? []).flatMap(orcadShapeConverters_1.OrCadShapeConvert.graphicLineInst()),
	            ...(page.GraphicBoxInst ?? []).flatMap(orcadShapeConverters_1.OrCadShapeConvert.graphicBoxInst()),
	            ...(page.GraphicPolygonInst ?? []).flatMap(orcadShapeConverters_1.OrCadShapeConvert.graphicPolygonInst()),
	        ]);
	        const bbox = new Area_1.default();
	        bbox.extendFromSchModel(this.project, sch, this.project.layerSets[orcadLayers_1.ORCAD_SCH_LAYERSET], sch.warnings);
	        sch.edges_bbox = {
	            minx: Number.isFinite(bbox.xMin) ? bbox.xMin : -100,
	            miny: Number.isFinite(bbox.yMin) ? bbox.yMin : -100,
	            maxx: Number.isFinite(bbox.xMax) ? bbox.xMax : 100,
	            maxy: Number.isFinite(bbox.yMax) ? bbox.yMax : 100,
	        };
	        sch.initial_zoombox = {
	            minx: Number.isFinite(bbox.xMin) ? bbox.xMin : -100,
	            miny: Number.isFinite(bbox.yMin) ? bbox.yMin : -100,
	            maxx: Number.isFinite(bbox.xMax) ? bbox.xMax : 100,
	            maxy: Number.isFinite(bbox.yMax) ? bbox.yMax : 100,
	        };
	        const moduleIds = sch.modules.map((m) => m.ref);
	        if (new Set(moduleIds).size !== moduleIds.length) {
	            throw new Error(`Duplicate module ids found in schematic ${sch.designId}`);
	        }
	    }
	    extractModuleFromGlobalSymbol(sch, globalSymbol) {
	        const component = this.project.components[globalSymbol.SymbolName];
	        if (!component) {
	            throw new Error(`Could not find component for global symbol ${globalSymbol.SymbolName}`);
	        }
	        const symbol = component.symbols.find((s) => s.ref === globalSymbol.SymbolName);
	        if (!symbol) {
	            throw new Error(`Could not find symbol for global symbol ${globalSymbol.SymbolName}`);
	        }
	        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
	        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
	        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
	        let center = pointTransform([globalSymbol.LocX, globalSymbol.LocY]);
	        if (globalSymbol.Rotation % 2 === 1) {
	            center = oneWeirdTrick(center);
	        }
	        const module = {
	            ref: globalSymbol.DbId.toString(),
	            footprintRef: symbol.ref,
	            componentRef: component.name,
	            center,
	            layer: 'F',
	            transform: {
	                pos: [0, 0],
	                angle: globalSymbol.Rotation * 90,
	                mirrored: globalSymbol.Mirror === 1 ?
	                    [
	                        globalSymbol.Rotation === 1 || globalSymbol.Rotation === 3,
	                        globalSymbol.Rotation === 2 || globalSymbol.Rotation === 0,
	                    ]
	                    : [false, false],
	            },
	            name: `#PWR${(this.globalSymbolNum++).toString().padStart(3, '0')}`,
	            pinNets: {},
	            drawProperties: {},
	            metadata: {
	                Value: globalSymbol.Name,
	                'orcad.globalSymbol.name': globalSymbol.Name,
	                'orcad.globalSymbol.symbolName': globalSymbol.SymbolName,
	            },
	        };
	        if (globalSymbol.DisplayProperty) {
	            module.drawProperties[globalSymbol.DisplayProperty.Name] = {
	                value: globalSymbol.DisplayProperty.Name === 'Name' ? globalSymbol.Name : module.name,
	                pos: [
	                    globalSymbol.DisplayProperty.LocX + globalSymbol.LocX,
	                    globalSymbol.DisplayProperty.LocY + globalSymbol.LocY,
	                ],
	                layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.ValueLabel].id,
	                angle: globalSymbol.DisplayProperty.Rotation * 90,
	                justify: [-1, -1],
	                size: this.scaleFont(globalSymbol.DisplayProperty.Font),
	                thickness: 1,
	                mirror: [false, false],
	                hidden: false,
	            };
	        }
	        this.addSchematicModule(sch, module);
	        this.connectGlobalSymbolNets(sch, globalSymbol, symbol, module);
	    }
	    connectGlobalSymbolNets(sch, globalSymbol, symbol, module) {
	        if (symbol.pins.length === 0) {
	            this.logProcessResult({
	                type: 'global-symbol-no-signals',
	                message: `Global symbol ${globalSymbol.Name} symbol ${symbol.ref} has no pins. No signals will be connected.`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            return;
	        }
	        if (symbol.pins.length > 1) {
	            this.logProcessResult({
	                type: 'global-symbol-multiple-signals',
	                message: `Global symbol ${globalSymbol.Name} symbol ${symbol.ref} has multiple pins. No signals will be connected.`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            return;
	        }
	        let net = sch.nets[globalSymbol.Name];
	        if (!net) {
	            net = {
	                name: globalSymbol.Name,
	                connections: [],
	            };
	            sch.nets[globalSymbol.Name] = net;
	        }
	        const pinRef = symbol.pins[0].ref;
	        net.connections.push({
	            moduleRef: module.ref,
	            moduleName: module.name,
	            pinRef: pinRef,
	        });
	        module.pinNets[pinRef] = globalSymbol.Name;
	    }
	    getInstOccurenceForRefInstId(refInstId) {
	        const occurences = [];
	        for (const [, schema] of this.logicalSchemas) {
	            for (const schemaOccurence of schema.Occurrences.InstOccurrence) {
	                for (const occurence of schemaOccurence.InstOccurrence ?? []) {
	                    if (occurence.RefInstId === refInstId) {
	                        occurences.push([schema, occurence]);
	                    }
	                }
	            }
	        }
	        if (occurences.length > 1) {
	            throw new Error(`${occurences.length} inst occurences found for refInstId ${refInstId}`);
	        }
	        if (occurences.length === 1) {
	            return occurences[0][1];
	        }
	        return undefined;
	    }
	    extractModuleFromPartInst(sch, partInst) {
	        const refInstId = partInst.DbId.toString();
	        const instOccurence = this.getInstOccurenceForRefInstId(refInstId);
	        const referenceDesignator = instOccurence?.UserProperty?.find((p) => p.Name === 'Part Reference')?.Value ??
	            `${partInst.Reference}${partInst.DeviceDesignator}`;
	        const fullModuleName = instOccurence?.UserProperty?.find((p) => p.Name === 'Reference')?.Value ?? partInst.Reference;
	        const componentRef = `${partInst.LibName}::${partInst.PkgName}`;
	        const component = this.project.components[componentRef];
	        if (!component) {
	            sch.warnings.push(`Could not find component for ${componentRef} (${referenceDesignator})`);
	            throw new Error(`Could not find component for ${componentRef} (${referenceDesignator})`);
	        }
	        let symbol = component.symbols.find((s) => s.ref === `${partInst.GraphicName}${partInst.DeviceDesignator}`) ??
	            component.symbols.find((s) => s.ref === `${partInst.GraphicName}`);
	        if (!symbol && (0, alphaDesignatorToConstant_1.isAlphaDesignator)(partInst.DeviceDesignator)) {
	            const physicalPartIndex = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(partInst.DeviceDesignator);
	            symbol = component.symbols.find((s) => s.name === `${partInst.GraphicName}-${physicalPartIndex}`);
	            this.logProcessResult({
	                type: 'symbol-lookup-fallback-physical-part-index',
	                message: `Found symbol for ${componentRef}::${partInst.GraphicName} (${referenceDesignator}) using backup physical part index ${physicalPartIndex}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        if (!symbol) {
	            sch.warnings.push(`Could not find symbol for ${componentRef}::${partInst.GraphicName} (${referenceDesignator})`);
	            return;
	        }
	        const metadata = {};
	        for (const userProp of partInst.UserProperty ?? []) {
	            metadata['Value'] = partInst.PartValue;
	            metadata[userProp.Name] = userProp.Value;
	        }
	        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
	        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
	        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
	        let center = pointTransform([partInst.LocX, partInst.LocY]);
	        if (partInst.Rotation % 2 === 1) {
	            center = oneWeirdTrick(center);
	        }
	        const partModule = {
	            ref: refInstId,
	            name: referenceDesignator,
	            fullModuleName,
	            footprintRef: symbol.ref,
	            componentRef: component.name,
	            center,
	            layer: 'F',
	            transform: {
	                pos: [0, 0],
	                angle: partInst.Rotation * 90,
	                mirrored: partInst.Mirror === 1 ?
	                    [partInst.Rotation === 1 || partInst.Rotation === 3, partInst.Rotation === 2 || partInst.Rotation === 0]
	                    : [false, false],
	            },
	            pinNets: {},
	            drawProperties: {},
	            metadata,
	        };
	        {
	            const namedValues = Object.fromEntries([
	                ['Part Reference', partInst.Reference],
	                ['Value', partInst.PartValue],
	                ...Object.entries(partInst.UserProperty ?? []).map(([k, v]) => [k, v.Value]),
	            ]);
	            for (const prop of partInst.DisplayProperty ?? []) {
	                let layerType = sigma_engine_shared_1.SchLayerType.Label;
	                switch (prop.Name) {
	                    case 'Name':
	                        layerType = sigma_engine_shared_1.SchLayerType.NameLabel;
	                        break;
	                    case 'Value':
	                        layerType = sigma_engine_shared_1.SchLayerType.ValueLabel;
	                        break;
	                }
	                const namedValue = namedValues[prop.Name];
	                if (namedValue === undefined) {
	                    sch.warnings.push(`Could not find value for defined display property ${prop.Name} on ${partInst.Reference}`);
	                    continue;
	                }
	                partModule.drawProperties[prop.Name] = {
	                    value: namedValue,
	                    pos: [prop.LocX + partInst.LocX, prop.LocY + partInst.LocY],
	                    layer: orcadLayers_1.OrCadSchLayerMap[layerType].id,
	                    angle: prop.Rotation * 90,
	                    justify: [-1, -1],
	                    size: this.scaleFont(prop.Font),
	                    thickness: 1,
	                    hidden: false,
	                    ref_designator: prop.Name === 'Part Reference',
	                    mirror: [false, false],
	                };
	            }
	        }
	        this.addSchematicModule(sch, partModule);
	    }
	    addSchematicModule(sch, partModule) {
	        sch.modules.push(partModule);
	        const matchName = partModule.fullModuleName ?? partModule.name;
	        let projectModule = this.project.modules.find((m) => m.name === matchName);
	        if (!projectModule) {
	            projectModule = {
	                name: partModule.fullModuleName ?? partModule.name,
	                instances: [],
	                componentRef: partModule.componentRef,
	                value: partModule.metadata?.['Value']?.toString(),
	            };
	            this.project.modules.push(projectModule);
	        }
	        if (!projectModule.value && partModule.metadata?.['Value']) {
	            projectModule.value = partModule.metadata['Value'].toString();
	        }
	        if (partModule.metadata) {
	            projectModule.metadata = (0, addMetadataWithoutDuplicates_1.addMetadataWithoutDuplicates)(projectModule.metadata, partModule.metadata);
	        }
	        projectModule.instances.push({
	            ref: partModule.ref,
	            designId: sch.designId,
	        });
	    }
	    applyNetlist(netlist) {
	        if (!netlist.network?.nets?.length) {
	            this.logProcessResult({
	                type: 'netlist-network-missing',
	                message: 'Netlist network section is empty. Module pin nets will not be populated.',
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            });
	            return;
	        }
	        for (const net of netlist.network?.nets ?? []) {
	            this.project.globalNets[net.name] = {
	                name: net.name,
	            };
	            for (const pin of net.pins ?? []) {
	                const appliedPins = new Set();
	                const module = this.project.modules.find((m) => m.name === pin.referenceDesignator);
	                if (!module) {
	                    this.logProcessResult({
	                        type: 'netlist-module-missing',
	                        message: `Netlist references module ${pin.referenceDesignator} that does not exist in project.`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    });
	                    continue;
	                }
	                const component = this.project.components[module.componentRef];
	                if (!component) {
	                    throw new Error(`Component ${module.componentRef} not found for module ${module.name}`);
	                }
	                for (const instance of module.instances) {
	                    const schematic = this.project.designs[instance.designId];
	                    if (!schematic) {
	                        throw new Error(`Schematic ${instance.designId} not found for module ${module.name}`);
	                    }
	                    const schematicModule = schematic.modules.find((m) => m.ref === instance.ref);
	                    if (!schematicModule) {
	                        throw new Error(`Module ${instance.ref} not found in schematic ${instance.designId} but has an instance pointer`);
	                    }
	                    const symbol = component.symbols.find((s) => s.ref === schematicModule.footprintRef);
	                    if (!symbol) {
	                        throw new Error(`Symbol ${schematicModule.footprintRef} not found for component ${component.name} module ${schematicModule.name}`);
	                    }
	                    const symbolPin = symbol.pins.find((p) => p.number === pin.pin);
	                    if (!symbolPin) {
	                        continue;
	                    }
	                    schematicModule.pinNets[symbolPin.ref] = net.name;
	                    let schematicNet = schematic.nets[net.name];
	                    if (!schematicNet) {
	                        schematicNet = {
	                            name: net.name,
	                            connections: [],
	                        };
	                        schematic.nets[net.name] = schematicNet;
	                    }
	                    schematicNet.connections.push({
	                        moduleRef: instance.ref,
	                        moduleName: module.name,
	                        pinRef: symbolPin.ref,
	                    });
	                    appliedPins.add(pin.pin);
	                }
	                if (appliedPins.size !== 1) {
	                    this.logProcessResult({
	                        type: 'netlist-pin-ambiguous',
	                        message: `Netlist references pin ${pin.pin} on module ${pin.referenceDesignator} that is ambiguous. Found ${appliedPins.size} matches.`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    });
	                }
	            }
	        }
	    }
	    extractNetFromNetScalar(sch, netScalar) {
	        for (const wire of netScalar.WireScalars ?? []) {
	            this.extractWireFromWireScalar(sch, wire, false);
	        }
	    }
	    extractNetFromNetBus(sch, netBus) {
	        for (const wire of netBus.WireBus ?? []) {
	            this.extractWireFromWireScalar(sch, wire, true);
	        }
	        for (const netScalar of netBus.NetScalar ?? []) {
	            this.extractNetFromNetScalar(sch, netScalar);
	        }
	    }
	    extractWireFromWireScalar(sch, wire, bus = false) {
	        const layer = bus ? orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Bus] : orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire];
	        const width = bus ? wire.LineWidth : wire.LineWidth * OrCadDSNTransformer.WIRE_WIDTH_MULTIPLIER;
	        sch.drawings[layer.id] ??= [];
	        sch.drawings[layer.id].push({
	            type: 'segment',
	            start: [wire.StartX, wire.StartY],
	            end: [wire.EndX, wire.EndY],
	            width: width,
	        });
	        const juctionLayer = bus ? orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.BusJunction] : orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Junction];
	        wire.Junction?.forEach((j) => {
	            sch.drawings[juctionLayer.id] ??= [];
	            sch.drawings[juctionLayer.id].push({
	                type: 'junction',
	                position: [j.LocX, j.LocY],
	                radiusOverride: 1.25,
	            });
	        });
	        for (const alias of wire.Alias ?? []) {
	            this.extractTextFromAlias(sch, alias);
	        }
	    }
	    extractCommentText(sch, commentText) {
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id] ??= [];
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id].push({
	            type: 'text',
	            value: commentText.Name,
	            pos: [commentText.LocX, commentText.LocY],
	            angle: commentText.Rotation ?? 0,
	            justify: [-1, 0],
	            size: this.scaleFont(commentText.Font),
	            thickness: 1,
	        });
	    }
	    extractTextFromAlias(sch, alias) {
	        if (!alias.Name || !alias.DbId || !alias.LocX || !alias.LocY || !alias.Font) {
	            return;
	        }
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id] ??= [];
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id].push({
	            type: 'text',
	            value: alias.Name,
	            pos: [alias.LocX, alias.LocY],
	            angle: alias.Rotation ?? 0,
	            justify: [-1, 1],
	            size: this.scaleFont(alias.Font),
	            thickness: 1,
	        });
	    }
	    extractPortSymbol(portSymbol) {
	        const symbol = {
	            ref: portSymbol.Name,
	            name: portSymbol.Name,
	            drawings: [],
	            pins: [],
	            metadata: {
	                'orcad.symbol.boundingbox': portSymbol.BoundingBox,
	            },
	        };
	        const bboxCenter = [
	            (portSymbol.BoundingBox.X1 + portSymbol.BoundingBox.X2) / 2,
	            (portSymbol.BoundingBox.Y1 + portSymbol.BoundingBox.Y2) / 2,
	        ];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
	        symbol.drawings = orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(portSymbol).map((drawing) => {
	            return {
	                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
	                drawing,
	            };
	        });
	        this.portSymbols[portSymbol.Name] = symbol;
	    }
	    extractOffPageSymbol(offPageSymbol) {
	        const bboxCenter = [
	            (offPageSymbol.BoundingBox.X1 + offPageSymbol.BoundingBox.X2) / 2,
	            (offPageSymbol.BoundingBox.Y1 + offPageSymbol.BoundingBox.Y2) / 2,
	        ];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
	        const symbol = {
	            ref: offPageSymbol.Name,
	            name: offPageSymbol.Name,
	            drawings: [
	                ...orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(offPageSymbol).map((drawing) => {
	                    return {
	                        layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
	                        drawing,
	                    };
	                }),
	            ],
	            pins: [],
	            metadata: {
	                'orcad.symbol.boundingbox': offPageSymbol.BoundingBox,
	            },
	        };
	        this.offPageSymbols[offPageSymbol.Name] = symbol;
	    }
	    extractOffPageConnector(sch, connector) {
	        const symbol = this.offPageSymbols[String(connector.SymbolName)];
	        if (!symbol) {
	            sch.warnings.push(`Could not find off page symbol for ${connector.SymbolName}`);
	            return;
	        }
	        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
	        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
	        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
	        let symbolCenterOffset = pointTransform([0, 0]);
	        if (connector.Rotation % 2 === 1) {
	            symbolCenterOffset = oneWeirdTrick(symbolCenterOffset);
	        }
	        const mirrored = connector.Mirror === 1 ?
	            [connector.Rotation === 1 || connector.Rotation === 3, connector.Rotation === 2 || connector.Rotation === 0]
	            : [false, false];
	        const symbolDrawings = symbol.drawings.map((d) => (0, Transform_1.transformDrawing)((0, Transform_1.transformDrawing)(d.drawing, { mirrored, angle: connector.Rotation * 90, pos: [0, 0] }), {
	            mirrored: [false, false],
	            angle: 0,
	            pos: symbolCenterOffset,
	        }));
	        const namedValues = Object.fromEntries([
	            ['Name', connector.Name],
	            ...(connector.OffPageConnectorUserProp ?? []).map((prop) => [prop.Name, prop.Value]),
	        ]);
	        const textDrawings = (connector.OffPageConnectorDisplayProp ?? []).flatMap((displayProp) => {
	            const namedValue = namedValues[displayProp.Name];
	            if (namedValue === undefined) {
	                sch.warnings.push(`Could not find value for defined display property ${displayProp.Name} on ${connector.SymbolName}`);
	                return [];
	            }
	            const fontSize = this.scaleFont(displayProp.Font);
	            const textLoc = [displayProp.LocX, displayProp.LocY];
	            return [
	                {
	                    type: 'text',
	                    value: namedValue,
	                    pos: (0, Transform_1.translatePoint)(textLoc, [0, fontSize]),
	                    angle: displayProp.Rotation * 90,
	                    justify: [-1, -1],
	                    size: fontSize,
	                    mirror: [false, false],
	                    thickness: 1,
	                },
	            ];
	        });
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id] ??= [];
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id].push({
	            type: 'group',
	            pos: [connector.LocX, connector.LocY],
	            angle: 0,
	            drawings: [...textDrawings, ...symbolDrawings],
	            metadata: {},
	        });
	    }
	    extractPort(sch, port) {
	        const symbol = this.portSymbols[String(port.SymbolName)];
	        if (!symbol) {
	            sch.warnings.push(`Could not find port symbol for ${port.SymbolName}`);
	            return;
	        }
	        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
	        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
	        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
	        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
	        let symbolCenterOffset = pointTransform([0, 0]);
	        if (port.Rotation && port.Rotation % 2 === 1) {
	            symbolCenterOffset = oneWeirdTrick(symbolCenterOffset);
	        }
	        const mirrored = port.Mirror === 1 ?
	            [port.Rotation === 1 || port.Rotation === 3, port.Rotation === 2 || port.Rotation === 0]
	            : [false, false];
	        const symbolDrawings = symbol.drawings.map((d) => (0, Transform_1.transformDrawing)((0, Transform_1.transformDrawing)(d.drawing, { mirrored, angle: (port.Rotation ?? 0) * 90, pos: [0, 0] }), {
	            mirrored: [false, false],
	            angle: 0,
	            pos: symbolCenterOffset,
	        }));
	        const namedValues = Object.fromEntries([
	            ['Name', port.Name],
	            ...(port.PortUserProp ?? []).map((prop) => [prop.Name, prop.Value]),
	        ]);
	        const textDrawings = (port.PortDisplayProp ?? []).flatMap((displayProp) => {
	            const namedValue = namedValues[displayProp.Name];
	            if (namedValue === undefined) {
	                sch.warnings.push(`Could not find value for defined display property ${displayProp.Name} on ${port.Name}`);
	                return [];
	            }
	            const fontSize = this.scaleFont(displayProp.Font);
	            const textLoc = [displayProp.LocX, displayProp.LocY];
	            return [
	                {
	                    type: 'text',
	                    value: namedValue,
	                    pos: (0, Transform_1.translatePoint)(textLoc, [0, fontSize]),
	                    angle: displayProp.Rotation * 90,
	                    justify: [-1, -1],
	                    size: fontSize,
	                    mirror: [false, false],
	                    thickness: 1,
	                },
	            ];
	        });
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id] ??= [];
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id].push({
	            type: 'group',
	            pos: [port.LocX, port.LocY],
	            angle: 0,
	            drawings: [...textDrawings, ...symbolDrawings],
	            metadata: {},
	        });
	    }
	    scaleFont(font) {
	        return Math.abs(font.Height / 2);
	    }
	    extractBusEntry(sch, busEntry) {
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id] ??= [];
	        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id].push({
	            type: 'segment',
	            start: [busEntry.BusX, busEntry.BusY],
	            end: [busEntry.EndX, busEntry.EndY],
	            width: OrCadDSNTransformer.DEFAULT_WIRE_WIDTH,
	        });
	    }
	}
	orcadDsnTransformer.OrCadDSNTransformer = OrCadDSNTransformer;
	
	return orcadDsnTransformer;
}

var hasRequiredParseOrCADProject;

function requireParseOrCADProject () {
	if (hasRequiredParseOrCADProject) return parseOrCADProject;
	hasRequiredParseOrCADProject = 1;
	Object.defineProperty(parseOrCADProject, "__esModule", { value: true });
	parseOrCADProject.parseProject = parseProject;
	const sigma_engine_shared_1 = requireDist$1();
	const orcadValidation_1 = requireOrcadValidation();
	const mergeDisjointComponentsUtil_1 = requireMergeDisjointComponentsUtil();
	const OrcadDsnOrchestratedTransformer_1 = requireOrcadDsnOrchestratedTransformer();
	const OrcadDsnUtils_1 = requireOrcadDsnUtils();
	const orcadDsnTransformer_1 = requireOrcadDsnTransformer();
	const orcadLayers_1 = requireOrcadLayers();
	const orcadLogicalSchematicReferenceResolver_1 = requireOrcadLogicalSchematicReferenceResolver();
	const ModelText_1 = requireModelText();
	const orcadIntersheetReferences_1 = requireOrcadIntersheetReferences();
	const OrcadDsnSymbolUtils_1 = requireOrcadDsnSymbolUtils();
	const OrCadIntersheetReferenceResultType = 'orcad-intersheet-reference';
	const OrCadStoredIntersheetReferenceResultType = 'iref-stored-uncorroborated';
	const OrCadIntersheetReferenceMismatchResultType = 'iref-computed-mismatch';
	const IntersheetReferencePropertyName = 'IREF';
	const OnlPlacementOmittedPropertyKeys = new Set(['TIMESTAMP', 'SOURCEPACKAGE', 'VALUE']);
	const OnlPlacementOnlyPropertyKeys = new Set(['BOMIGNORE', 'MODEL3D', 'OFFSET3D', 'PACKTYPE', 'SPLITINST']);
	function onlPropertyKey(name) {
	    return name.replace(/[\s_-]/g, '').toUpperCase();
	}
	function getDefaultSettings() {
	    return {
	        mergeDisjointComponents: false,
	        legacyParser: false,
	        combobulationDebug: false,
	        transformOpts: {},
	    };
	}
	function hasReferencedProjectFile(def, relativeToFile, referencedFile, referencedViewName) {
	    return ((0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(collectLogicalSchematicEntries(def), relativeToFile, referencedFile, referencedViewName).status === 'resolved');
	}
	function auditReferencedProjectFiles(def) {
	    const entries = collectLogicalSchematicEntries(def);
	    const unresolvedReferences = new Map();
	    const viewResolvedReferences = new Map();
	    for (const [, xmlSch] of Object.entries(def.schematics)) {
	        if (isValidOrCADLogicalSchematic(xmlSch)) {
	            for (const referencedFile of xmlSch.validationResult.data.referencedFiles ?? []) {
	                if (hasReferencedProjectFile(def, xmlSch.originalFileName, referencedFile)) {
	                    continue;
	                }
	                const viewNames = referencedViewNames(xmlSch.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema], referencedFile);
	                const filenameResolution = (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(entries, xmlSch.originalFileName, referencedFile);
	                if (filenameResolution.status === 'resolved') {
	                    continue;
	                }
	                const viewResolutions = viewNames.map((viewName) => ({
	                    viewName,
	                    resolution: (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(entries, xmlSch.originalFileName, referencedFile, viewName),
	                }));
	                const resolvedViews = viewResolutions.filter((row) => row.resolution.status === 'resolved' && row.resolution.matchKind === 'view');
	                const resolvedFiles = new Set(resolvedViews.map((row) => row.resolution.candidate.sourceFile));
	                if (resolvedViews.length > 0 && resolvedViews.length === viewResolutions.length && resolvedFiles.size === 1) {
	                    for (const { resolution, viewName } of resolvedViews) {
	                        const key = `${(0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(xmlSch.originalFileName)}\0${resolution.normalizedPath}\0${viewName.toLowerCase()}`;
	                        viewResolvedReferences.set(key, {
	                            referencedFrom: xmlSch.originalFileName,
	                            referencedFile,
	                            normalizedPath: resolution.normalizedPath,
	                            resolvedFile: resolution.candidate.sourceFile,
	                            viewName,
	                            matchKind: 'view',
	                        });
	                    }
	                    continue;
	                }
	                const ambiguousResolution = viewResolutions.find((row) => row.resolution.status === 'unresolved' && row.resolution.reason === 'ambiguous')?.resolution;
	                const resolution = ambiguousResolution ?? filenameResolution;
	                if (resolution.status === 'resolved') {
	                    continue;
	                }
	                const key = `${(0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(xmlSch.originalFileName)}\0${resolution.normalizedPath}`;
	                unresolvedReferences.set(key, {
	                    referencedFrom: xmlSch.originalFileName,
	                    referencedFile,
	                    normalizedPath: resolution.normalizedPath,
	                    reason: resolution.reason,
	                    candidates: resolution.candidates,
	                });
	            }
	        }
	    }
	    return {
	        hasValidSchematics: entries.length > 0,
	        unresolvedReferences: Array.from(unresolvedReferences.values()),
	        viewResolvedReferences: Array.from(viewResolvedReferences.values()),
	    };
	}
	function referencedViewNames(design, referencedFile) {
	    const normalizedReference = referencedFile.replace(/\\/g, '/').toLowerCase();
	    const names = new Set();
	    for (const schematic of design.Schematic) {
	        for (const page of schematic.Page) {
	            for (const instance of [...(page.DrawnInst ?? []), ...(page.PartInst ?? [])]) {
	                if (instance.ContentsLibName.replace(/\\/g, '/').toLowerCase() === normalizedReference &&
	                    instance.ContentsViewName) {
	                    names.add(instance.ContentsViewName);
	                }
	            }
	        }
	    }
	    return [...names].sort((left, right) => left.localeCompare(right));
	}
	function isValidOrCADLogicalSchematic(schematic) {
	    return Boolean(schematic.validationResult?.isValid &&
	        schematic.validationResult.data.fileFormat.cadTool === 'orcad' &&
	        (schematic.validationResult.data.fileFormat.type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematicXml ||
	            schematic.validationResult.data.fileFormat.type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic) &&
	        schematic.validationResult.data.metadata?.[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema]);
	}
	function logicalSchematicDedupeKey(fileName) {
	    return (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(fileName).replace(/\.(dsn|xml)$/i, '');
	}
	function logicalSchematicPreference(schematic) {
	    const type = schematic.validationResult?.data?.fileFormat.type;
	    if (type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic) {
	        return 2;
	    }
	    if (type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematicXml) {
	        return 1;
	    }
	    return 0;
	}
	function preferDsnLogicalSchemas(def) {
	    const selectedByLogicalPath = new Map();
	    for (const [key, schematic] of Object.entries(def.schematics)) {
	        if (!isValidOrCADLogicalSchematic(schematic)) {
	            continue;
	        }
	        const logicalKey = logicalSchematicDedupeKey(schematic.originalFileName);
	        const preference = logicalSchematicPreference(schematic);
	        const selected = selectedByLogicalPath.get(logicalKey);
	        if (!selected || preference > selected.preference) {
	            selectedByLogicalPath.set(logicalKey, { key, preference });
	        }
	    }
	    const selectedKeys = new Set(Array.from(selectedByLogicalPath.values()).map((selected) => selected.key));
	    const schematics = Object.fromEntries(Object.entries(def.schematics).filter(([key, schematic]) => !isValidOrCADLogicalSchematic(schematic) || selectedKeys.has(key)));
	    return { ...def, schematics };
	}
	function collectLogicalSchematicEntries(def) {
	    const entries = [];
	    for (const [, schematic] of Object.entries(def.schematics)) {
	        if (!isValidOrCADLogicalSchematic(schematic)) {
	            continue;
	        }
	        entries.push({
	            sourceFile: schematic.originalFileName,
	            schematicViewNames: schematic.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema].Schematic.map((entry) => entry.Name),
	            type: schematic.validationResult.data.fileFormat.type,
	            design: schematic.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema],
	        });
	    }
	    return entries;
	}
	function findLogicalSchematicEntry(entries, relativeToFile, referencedFile, referencedViewName) {
	    const resolution = (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(entries, relativeToFile, referencedFile, referencedViewName);
	    return resolution.status === 'resolved' ? resolution.candidate : undefined;
	}
	function findOccurrenceByRefInstId(occurrences, refInstId) {
	    for (const occurrence of occurrences ?? []) {
	        if (occurrence.RefInstId === refInstId) {
	            return occurrence;
	        }
	        const child = findOccurrenceByRefInstId(occurrence.InstOccurrence, refInstId);
	        if (child) {
	            return child;
	        }
	    }
	    return undefined;
	}
	function cloneOccurrence(occurrence) {
	    return {
	        ...occurrence,
	        UserProperty: occurrence.UserProperty?.map((property) => ({ ...property })),
	        DisplayProperty: occurrence.DisplayProperty?.map((property) => ({ ...property })),
	        PortOccurrence: occurrence.PortOccurrence?.map((port) => ({
	            ...port,
	            UserProperty: port.UserProperty?.map((property) => ({ ...property })),
	            DisplayProperty: port.DisplayProperty?.map((property) => ({ ...property })),
	        })),
	        TitleBlockOccurrence: occurrence.TitleBlockOccurrence?.map((titleBlock) => ({
	            ...titleBlock,
	            UserProperty: titleBlock.UserProperty?.map((property) => ({ ...property })),
	            DisplayProperty: titleBlock.DisplayProperty?.map((property) => ({ ...property })),
	        })),
	        InstOccurrence: occurrence.InstOccurrence?.map(cloneOccurrence),
	    };
	}
	function mergeOccurrenceUserProperties(base, override) {
	    if (!override?.length) {
	        return base?.map((property) => ({ ...property }));
	    }
	    const merged = new Map();
	    for (const property of base ?? []) {
	        merged.set(property.Name, { ...property });
	    }
	    for (const property of override) {
	        merged.set(property.Name, { ...property });
	    }
	    return Array.from(merged.values());
	}
	function mergeOccurrenceWithOverride(base, override) {
	    if (!override) {
	        return cloneOccurrence(base);
	    }
	    const merged = cloneOccurrence(base);
	    merged.UserProperty = mergeOccurrenceUserProperties(merged.UserProperty, override.UserProperty);
	    merged.DisplayProperty = override.DisplayProperty?.map((property) => ({ ...property })) ?? merged.DisplayProperty;
	    return merged;
	}
	function linkedTargetRootChildren(sourceOccurrence, targetDesign, targetViewName) {
	    const targetChildren = occurrenceChildrenForSchematic(targetDesign, targetViewName);
	    if (!targetChildren.length) {
	        return sourceOccurrence.InstOccurrence ?? [];
	    }
	    return targetChildren.map((targetChild) => mergeOccurrenceWithOverride(targetChild, findOccurrenceByRefInstId(sourceOccurrence.InstOccurrence, targetChild.RefInstId)));
	}
	function occurrenceChildrenForSchematic(targetDesign, targetViewName) {
	    const targetSchematic = targetDesign.Schematic.find((schematic) => schematic.Name.toLowerCase() === targetViewName.toLowerCase());
	    const targetIds = new Set(targetSchematic?.Page.flatMap((page) => [
	        ...(page.DrawnInst ?? []).map((instance) => String(instance.DbId)),
	        ...(page.PartInst ?? []).map((instance) => String(instance.DbId)),
	    ]) ?? []);
	    const candidates = [];
	    const visit = (occurrence) => {
	        const children = occurrence.InstOccurrence ?? [];
	        const score = children.filter((child) => targetIds.has(child.RefInstId)).length;
	        if (score > 0) {
	            candidates.push({ children, score });
	        }
	        for (const child of children) {
	            visit(child);
	        }
	    };
	    for (const occurrence of targetDesign.Occurrences.InstOccurrence) {
	        visit(occurrence);
	    }
	    const bestScore = Math.max(0, ...candidates.map((candidate) => candidate.score));
	    const best = candidates.filter((candidate) => candidate.score === bestScore);
	    if (best.length === 1) {
	        return best[0].children;
	    }
	    return (targetDesign.Occurrences.InstOccurrence.find((occurrence) => occurrence.RefInstId === '-1')?.InstOccurrence ?? []);
	}
	function collectOnlPlacementCatalog(def) {
	    const placements = new Map();
	    const fileGroups = [
	        def.schematics,
	        def.boards,
	        def.netlists,
	        def.boms,
	        def.other,
	    ];
	    for (const group of fileGroups) {
	        for (const file of Object.values(group ?? {})) {
	            const netlist = file.validationResult?.data?.metadata?.[orcadValidation_1.OrCADXProjectMetadataKey.ONLNetlist];
	            for (const component of netlist?.placement?.components ?? []) {
	                for (const place of component.places ?? []) {
	                    const key = normalizeOnlReference(place.refdes);
	                    if (key && !placements.has(key)) {
	                        placements.set(key, place);
	                    }
	                }
	            }
	        }
	    }
	    return placements;
	}
	function normalizeOnlReference(reference) {
	    const normalized = reference?.trim().toUpperCase();
	    return normalized && !normalized.endsWith('?') ? normalized : undefined;
	}
	function onlPropertyValue(value) {
	    if (value === true) {
	        return 'TRUE';
	    }
	    if (value === false) {
	        return 'FALSE';
	    }
	    return String(value);
	}
	function userPropertiesFromOnlPlacement(place) {
	    const properties = [];
	    for (const [name, value] of Object.entries(place.properties ?? {})) {
	        if (OnlPlacementOmittedPropertyKeys.has(onlPropertyKey(name))) {
	            continue;
	        }
	        properties.push({ Name: name, Value: onlPropertyValue(value) });
	    }
	    return properties;
	}
	function hasDsnLogicalSchema(def) {
	    return collectLogicalSchematicEntries(def).some((entry) => entry.type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic);
	}
	function hasAuthoritativeDsnCatalogMetadata(module) {
	    const metadata = module.metadata ?? {};
	    return typeof metadata.PN === 'string' && typeof metadata.DESCRIPTION === 'string';
	}
	function applyOnlPlacementPropertiesToModule(module, place) {
	    if (hasAuthoritativeDsnCatalogMetadata(module)) {
	        return;
	    }
	    const metadata = new Map(Object.entries(module.metadata ?? {}));
	    const value = place.properties.Value;
	    if (typeof value === 'string' || typeof value === 'number') {
	        module.value = String(value);
	        metadata.set('Value', String(value));
	    }
	    for (const property of userPropertiesFromOnlPlacement(place)) {
	        metadata.set(property.Name, property.Value);
	    }
	    for (const name of Array.from(metadata.keys())) {
	        if (OnlPlacementOnlyPropertyKeys.has(onlPropertyKey(name))) {
	            metadata.delete(name);
	        }
	    }
	    module.metadata = Object.fromEntries(metadata);
	}
	function applyOnlPlacementPropertiesToDsnProjectModel(project, def) {
	    if (!hasDsnLogicalSchema(def)) {
	        return;
	    }
	    const placements = collectOnlPlacementCatalog(def);
	    if (placements.size === 0) {
	        return;
	    }
	    for (const module of Object.values(project.modules)) {
	        const reference = normalizeOnlReference(module.name);
	        const placement = reference ? placements.get(reference) : undefined;
	        if (placement) {
	            applyOnlPlacementPropertiesToModule(module, placement);
	        }
	    }
	}
	function hierarchicalBlockPlacements(page) {
	    const placements = [];
	    for (const instance of page.DrawnInst ?? []) {
	        placements.push({
	            contentsLibName: instance.ContentsLibName,
	            contentsViewName: instance.ContentsViewName,
	            dbId: instance.DbId,
	        });
	    }
	    for (const instance of page.PartInst ?? []) {
	        if ((0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(instance)) {
	            placements.push({
	                contentsLibName: instance.ContentsLibName,
	                contentsViewName: instance.ContentsViewName,
	                dbId: instance.DbId,
	            });
	        }
	    }
	    return placements;
	}
	function normalizedIrefNetName(name) {
	    const normalized = name?.trim().toUpperCase();
	    return normalized ? normalized : undefined;
	}
	function strictPositiveInteger(value) {
	    if (value === undefined || !/^\+?\d+$/.test(value.trim())) {
	        return undefined;
	    }
	    const parsed = Number.parseInt(value, 10);
	    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
	}
	const MaxReportedIrefMismatches = 20;
	const TitleBlockPageNumberPropertyName = 'Page Number';
	function storedTitleBlockPageNumber(page) {
	    const numbers = new Set();
	    for (const titleBlock of page.TitleBlock ?? []) {
	        for (const property of titleBlock.UserProperty ?? []) {
	            if (property.Name !== TitleBlockPageNumberPropertyName) {
	                continue;
	            }
	            const parsed = strictPositiveInteger(property.Value);
	            if (parsed === undefined) {
	                return undefined;
	            }
	            numbers.add(parsed);
	        }
	    }
	    const [only] = numbers;
	    return numbers.size === 1 && only !== undefined ? only : undefined;
	}
	const TitleBlockSchematicPathPropertyName = 'Schematic Path';
	function storedOccurrencePageNumber(occurrence, page) {
	    const numbers = new Set();
	    for (const titleBlock of occurrence?.TitleBlockOccurrence ?? []) {
	        const properties = titleBlock.UserProperty ?? [];
	        const schematicPath = properties.find((property) => property.Name === TitleBlockSchematicPathPropertyName)?.Value;
	        if (schematicPath === undefined || schematicPath.split('/').pop() !== page.Name) {
	            continue;
	        }
	        for (const property of properties) {
	            if (property.Name !== TitleBlockPageNumberPropertyName) {
	                continue;
	            }
	            const parsed = strictPositiveInteger(property.Value);
	            if (parsed === undefined) {
	                return undefined;
	            }
	            numbers.add(parsed);
	        }
	    }
	    const [only] = numbers;
	    return numbers.size === 1 && only !== undefined ? only : undefined;
	}
	function storedDesignPageNumbers(design) {
	    const numbers = new Map();
	    const seen = new Set();
	    for (const schematic of design.Schematic) {
	        for (const page of schematic.Page) {
	            const number = storedTitleBlockPageNumber(page);
	            if (number === undefined || seen.has(number)) {
	                return undefined;
	            }
	            seen.add(number);
	            numbers.set(page, number);
	        }
	    }
	    return numbers.size > 0 ? numbers : undefined;
	}
	function designReferenceRanks(entries, root) {
	    const ranks = new Map([[root.sourceFile, 0]]);
	    const queue = [root];
	    while (queue.length > 0) {
	        const entry = queue.shift();
	        if (!entry) {
	            break;
	        }
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                for (const placement of hierarchicalBlockPlacements(page)) {
	                    if (!placement.contentsLibName) {
	                        continue;
	                    }
	                    const target = findLogicalSchematicEntry(entries, entry.sourceFile, placement.contentsLibName, placement.contentsViewName);
	                    if (!target || ranks.has(target.sourceFile)) {
	                        continue;
	                    }
	                    ranks.set(target.sourceFile, ranks.size);
	                    queue.push(target);
	                }
	            }
	        }
	    }
	    return ranks;
	}
	function rootLogicalSchematicEntry(entries, requestedSourceFile) {
	    if (requestedSourceFile !== undefined) {
	        const requestedKey = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(requestedSourceFile).toLowerCase();
	        return entries.find((entry) => (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(entry.sourceFile).toLowerCase() === requestedKey);
	    }
	    const referenced = new Set();
	    for (const entry of entries) {
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                for (const placement of hierarchicalBlockPlacements(page)) {
	                    if (!placement.contentsLibName) {
	                        continue;
	                    }
	                    const target = findLogicalSchematicEntry(entries, entry.sourceFile, placement.contentsLibName, placement.contentsViewName);
	                    if (target && target.sourceFile !== entry.sourceFile) {
	                        referenced.add(target.sourceFile);
	                    }
	                }
	            }
	        }
	    }
	    const roots = entries.filter((entry) => !referenced.has(entry.sourceFile));
	    return roots.length === 1 ? roots[0] : undefined;
	}
	function pageNetNames(page) {
	    const names = new Set();
	    for (const net of page.NetScalar ?? []) {
	        const name = normalizedIrefNetName(net.Name);
	        if (name) {
	            names.add(name);
	        }
	    }
	    for (const bus of page.NetBus ?? []) {
	        const busName = normalizedIrefNetName(bus.Name);
	        if (busName) {
	            names.add(busName);
	        }
	        for (const member of bus.NetScalar ?? []) {
	            const memberName = normalizedIrefNetName(member.Name);
	            if (memberName) {
	                names.add(memberName);
	            }
	        }
	    }
	    return names;
	}
	function designReferencesMissingFile(entries, entry) {
	    for (const schematic of entry.design.Schematic) {
	        for (const page of schematic.Page) {
	            for (const placement of hierarchicalBlockPlacements(page)) {
	                if (placement.contentsLibName &&
	                    !findLogicalSchematicEntry(entries, entry.sourceFile, placement.contentsLibName, placement.contentsViewName)) {
	                    return true;
	                }
	            }
	        }
	    }
	    return false;
	}
	function buildIntersheetPages(entries, pageBindings, requestedSourceFile, unnumberedDesigns) {
	    const root = rootLogicalSchematicEntry(entries, requestedSourceFile);
	    if (!root) {
	        return undefined;
	    }
	    const ranks = designReferenceRanks(entries, root);
	    const reached = entries.filter((entry) => ranks.has(entry.sourceFile));
	    for (const entry of reached) {
	        if (!storedDesignPageNumbers(entry.design)) {
	            unnumberedDesigns?.push(entry.sourceFile);
	        }
	    }
	    if (unnumberedDesigns?.length) {
	        return undefined;
	    }
	    if (reached.some((entry) => designReferencesMissingFile(entries, entry))) {
	        return undefined;
	    }
	    const entryByPage = new Map();
	    for (const entry of entries) {
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                entryByPage.set(page, { entry, schematicName: schematic.Name });
	            }
	        }
	    }
	    const pages = [];
	    for (const binding of pageBindings) {
	        const owner = entryByPage.get(binding.page);
	        const rank = owner ? ranks.get(owner.entry.sourceFile) : undefined;
	        if (!owner || rank === undefined) {
	            continue;
	        }
	        const localNumbers = storedDesignPageNumbers(owner.entry.design);
	        if (!localNumbers) {
	            return undefined;
	        }
	        const localNumber = storedOccurrencePageNumber(binding.occurrence, binding.page) ?? localNumbers.get(binding.page);
	        if (localNumber === undefined) {
	            return undefined;
	        }
	        pages.push({
	            sourceFile: owner.entry.sourceFile,
	            schematicName: owner.schematicName,
	            page: binding.page,
	            designId: binding.designId,
	            occurrencePath: binding.occurrencePath,
	            ...(binding.parentOccurrencePath === undefined ? {} : { parentOccurrencePath: binding.parentOccurrencePath }),
	            ...(binding.sheetSymbolDbId === undefined ? {} : { sheetSymbolDbId: binding.sheetSymbolDbId }),
	            localNumber,
	            designRank: rank,
	            projectNumber: 0,
	            hasPartInstance: (binding.page.PartInst ?? []).length > 0,
	            netNames: pageNetNames(binding.page),
	            flatNets: { atComponentPins: binding.flatNetIdsAtComponentPins, all: binding.flatNetIds },
	        });
	    }
	    pages.sort((left, right) => left.localNumber - right.localNumber ||
	        left.designRank - right.designRank ||
	        left.designId.localeCompare(right.designId));
	    pages.forEach((page, index) => {
	        page.projectNumber = index + 1;
	    });
	    return pages;
	}
	function placementParentPageNumbers(placements) {
	    const byOccurrencePath = new Map();
	    for (const placement of placements) {
	        const siblings = byOccurrencePath.get(placement.occurrencePath) ?? [];
	        siblings.push(placement);
	        byOccurrencePath.set(placement.occurrencePath, siblings);
	    }
	    const parents = new Map();
	    for (const placement of placements) {
	        const { parentOccurrencePath, sheetSymbolDbId } = placement;
	        if (parentOccurrencePath === undefined || sheetSymbolDbId === undefined) {
	            continue;
	        }
	        const hosts = (byOccurrencePath.get(parentOccurrencePath) ?? []).filter((candidate) => hierarchicalBlockPlacements(candidate.page).some((block) => block.dbId === sheetSymbolDbId));
	        const [only] = hosts;
	        if (hosts.length === 1 && only !== undefined) {
	            parents.set(placement.designId, only.projectNumber);
	        }
	    }
	    return parents;
	}
	function isUnverifiedIntersheetReference(host, name) {
	    return !host.hasPartInstance && !host.netNames.has(name);
	}
	function formatIrefValue(pageNumbers, unverified, parentPageNumber) {
	    const tokens = new Map();
	    for (const pageNumber of pageNumbers) {
	        if (pageNumber !== parentPageNumber) {
	            tokens.set(pageNumber, `${pageNumber}${unverified ? '!' : ''}`);
	        }
	    }
	    if (parentPageNumber !== undefined) {
	        tokens.set(parentPageNumber, `${parentPageNumber}^`);
	    }
	    if (tokens.size === 0) {
	        return undefined;
	    }
	    return Array.from(tokens.keys())
	        .sort((left, right) => left - right)
	        .map((pageNumber) => tokens.get(pageNumber) ?? '')
	        .join(',');
	}
	function storedIrefValue(userProperties) {
	    const stored = userProperties?.find((property) => property.Name === IntersheetReferencePropertyName)?.Value;
	    return stored !== undefined && stored.trim().length > 0 ? stored : undefined;
	}
	function pageCarriesOnlyGlobalSymbols(page) {
	    return !page.hasPartInstance && (page.page.Global ?? []).length > 0;
	}
	function flatNetsByObject(bindings, objectDbId) {
	    const byDesign = new Map();
	    for (const binding of bindings) {
	        const byDbId = byDesign.get(binding.designId) ?? new Map();
	        const flatNetIds = byDbId.get(objectDbId(binding)) ?? new Set();
	        for (const flatNetId of binding.flatNetIds) {
	            flatNetIds.add(flatNetId);
	        }
	        byDbId.set(objectDbId(binding), flatNetIds);
	        byDesign.set(binding.designId, byDbId);
	    }
	    return byDesign;
	}
	function componentPinPageNumbers(flatNetIds, pages) {
	    const numbers = [];
	    for (const page of pages) {
	        for (const flatNetId of flatNetIds) {
	            if (page.flatNets.atComponentPins.has(flatNetId)) {
	                numbers.push(page.projectNumber);
	                break;
	            }
	        }
	    }
	    return numbers;
	}
	function flatNetPageNumbers(flatNetIds, pages) {
	    const numbers = [];
	    for (const page of pages) {
	        for (const flatNetId of flatNetIds) {
	            if (page.flatNets.all.has(flatNetId)) {
	                numbers.push(page.projectNumber);
	                break;
	            }
	        }
	    }
	    return numbers;
	}
	function hostPageRepeatsConnector(host, connectorDbId, flatNetIds, connectorFlatNets) {
	    const byDbId = connectorFlatNets.get(host.designId);
	    if (!byDbId) {
	        return false;
	    }
	    for (const [otherDbId, otherFlatNetIds] of byDbId) {
	        if (otherDbId === connectorDbId) {
	            continue;
	        }
	        for (const flatNetId of otherFlatNetIds) {
	            if (flatNetIds.has(flatNetId)) {
	                return true;
	            }
	        }
	    }
	    return false;
	}
	const GridIrefValuePattern = /^\[Grid.+\]$/;
	const PageNumberIrefValuePattern = /^\d+[!^]?(,\d+[!^]?)*$/;
	var IntersheetReferenceForm;
	(function (IntersheetReferenceForm) {
	    IntersheetReferenceForm["PageNumber"] = "page_number";
	    IntersheetReferenceForm["Grid"] = "grid";
	    IntersheetReferenceForm["Ambiguous"] = "ambiguous";
	})(IntersheetReferenceForm || (IntersheetReferenceForm = {}));
	function designIntersheetReferenceForm(design) {
	    const stored = design.IntersheetReferenceValues;
	    if (stored === undefined) {
	        return IntersheetReferenceForm.PageNumber;
	    }
	    const values = stored.filter((value) => value.trim().length > 0);
	    if (values.length > 0 && values.every((value) => GridIrefValuePattern.test(value))) {
	        return IntersheetReferenceForm.Grid;
	    }
	    if (values.length > 0 && values.every((value) => PageNumberIrefValuePattern.test(value))) {
	        return IntersheetReferenceForm.PageNumber;
	    }
	    return IntersheetReferenceForm.Ambiguous;
	}
	function storedIntersheetReferences(entries, placements) {
	    const sourceFileByPage = new Map();
	    for (const entry of entries) {
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                sourceFileByPage.set(page, entry.sourceFile);
	            }
	        }
	    }
	    const placementsPerPage = new Map();
	    for (const placement of placements) {
	        placementsPerPage.set(placement.page, (placementsPerPage.get(placement.page) ?? 0) + 1);
	    }
	    const references = new Map();
	    let reusedPageConnectors = 0;
	    for (const placement of placements) {
	        const sourceFile = sourceFileByPage.get(placement.page);
	        if (sourceFile === undefined) {
	            continue;
	        }
	        if ((placementsPerPage.get(placement.page) ?? 0) > 1) {
	            reusedPageConnectors +=
	                (placement.page.Port ?? []).filter((port) => storedIrefValue(port.PortUserProp) !== undefined).length +
	                    (placement.page.OffPageConnector ?? []).filter((connector) => storedIrefValue(connector.OffPageConnectorUserProp) !== undefined).length;
	            continue;
	        }
	        const claim = (objectKind, objectDbId, value) => {
	            if (value === undefined) {
	                return;
	            }
	            references.set(intersheetReferenceLabelKey(placement.designId, objectKind, objectDbId), {
	                sourceFile,
	                designId: placement.designId,
	                objectKind,
	                objectDbId,
	                value,
	                source: orcadIntersheetReferences_1.OrCadIntersheetReferenceSource.Stored,
	            });
	        };
	        for (const port of placement.page.Port ?? []) {
	            claim(orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port, port.DbId, storedIrefValue(port.PortUserProp));
	        }
	        for (const connector of placement.page.OffPageConnector ?? []) {
	            claim(orcadIntersheetReferences_1.OrCadIntersheetObjectKind.OffPageConnector, connector.DbId, storedIrefValue(connector.OffPageConnectorUserProp));
	        }
	    }
	    return { references, reusedPageConnectors };
	}
	function computeProjectIntersheetReferences(def, transformResult, requestedSourceFile) {
	    const entries = collectLogicalSchematicEntries(def);
	    const { references: stored, reusedPageConnectors } = storedIntersheetReferences(entries, transformResult.pageNetBindings);
	    const uncorroborated = (reason) => ({
	        references: Array.from(stored.values()),
	        results: stored.size === 0 ?
	            []
	            : [
	                {
	                    type: OrCadStoredIntersheetReferenceResultType,
	                    message: `The design file's own intersheet reference was emitted unchecked on ${stored.size} connector(s) because ${reason}.`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                },
	            ],
	    });
	    const noReferences = {
	        ...uncorroborated('the project has no compiled pages to check it against'),
	        pagesByDesignId: new Map(),
	    };
	    if (entries.length === 0) {
	        return noReferences;
	    }
	    let unvaluedCount = 0;
	    for (const entry of entries) {
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                for (const port of page.Port ?? []) {
	                    unvaluedCount += storedIrefValue(port.PortUserProp) === undefined ? 1 : 0;
	                }
	                for (const connector of page.OffPageConnector ?? []) {
	                    unvaluedCount += storedIrefValue(connector.OffPageConnectorUserProp) === undefined ? 1 : 0;
	                }
	            }
	        }
	    }
	    const unnumberedDesigns = [];
	    const pages = buildIntersheetPages(entries, transformResult.pageNetBindings, requestedSourceFile, unnumberedDesigns);
	    if (!pages) {
	        const reason = unnumberedDesigns.length > 0 ?
	            `these designs carry no complete stored title-block page numbering: ${unnumberedDesigns.join(', ')}`
	            : 'the requested root design is not part of this project, or the project does not carry a design a block on it references';
	        const unchecked = uncorroborated('the project page numbering could not be resolved');
	        return {
	            references: unchecked.references,
	            pagesByDesignId: new Map(),
	            results: [
	                ...unchecked.results,
	                {
	                    type: OrCadIntersheetReferenceResultType,
	                    message: `Intersheet references were left empty on ${unvaluedCount} connector(s): the project page numbering could not be resolved because ${reason}.`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                },
	            ],
	        };
	    }
	    const numberedPages = new Set(pages.map((page) => page.page));
	    const uncompiledPages = new Set(transformResult.uncompiledNetObjects.filter((object) => object.portDbId === undefined).map((object) => object.page));
	    const uncompiledPorts = new Map();
	    for (const object of transformResult.uncompiledNetObjects) {
	        if (object.portDbId === undefined) {
	            continue;
	        }
	        const portDbIds = uncompiledPorts.get(object.page) ?? new Set();
	        portDbIds.add(object.portDbId);
	        uncompiledPorts.set(object.page, portDbIds);
	    }
	    const parentPageNumbers = placementParentPageNumbers(pages);
	    const portFlatNets = flatNetsByObject(transformResult.portNetBindings, (binding) => binding.portDbId);
	    const connectorFlatNets = flatNetsByObject(transformResult.offPageConnectorNetBindings, (binding) => binding.connectorDbId);
	    const tally = {
	        unbound: 0,
	        parentless: 0,
	        unreferenced: 0,
	        unnamed: 0,
	        gridForm: 0,
	        ambiguousForm: 0,
	        outsideProject: 0,
	        globalOnlyPage: 0,
	        uncompiled: 0,
	        unresolvedFlatNet: 0,
	    };
	    for (const entry of entries) {
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                if (numberedPages.has(page)) {
	                    continue;
	                }
	                for (const connector of page.OffPageConnector ?? []) {
	                    tally.outsideProject += storedIrefValue(connector.OffPageConnectorUserProp) === undefined ? 1 : 0;
	                }
	                for (const port of page.Port ?? []) {
	                    tally.outsideProject += storedIrefValue(port.PortUserProp) === undefined ? 1 : 0;
	                }
	            }
	        }
	    }
	    const referenceFormBySourceFile = new Map(entries.map((entry) => [entry.sourceFile, designIntersheetReferenceForm(entry.design)]));
	    const unresolvedFlatNets = new Map(transformResult.unresolvedFlatNets.map((net) => [net.flatNetId, net.reason]));
	    const unresolvedReasons = new Set();
	    const mismatches = [];
	    const unresolvedReason = (flatNetIds) => {
	        for (const flatNetId of flatNetIds) {
	            const reason = unresolvedFlatNets.get(flatNetId);
	            if (reason !== undefined) {
	                return reason;
	            }
	        }
	        return undefined;
	    };
	    const references = [];
	    const claimed = new Set();
	    let uncorroboratedCount = 0;
	    const settle = (host, objectKind, objectDbId, computed) => {
	        const key = intersheetReferenceLabelKey(host.designId, objectKind, objectDbId);
	        claimed.add(key);
	        const storedReference = stored.get(key);
	        if (storedReference === undefined) {
	            if (computed !== undefined) {
	                references.push({
	                    sourceFile: host.sourceFile,
	                    designId: host.designId,
	                    objectKind,
	                    objectDbId,
	                    value: computed,
	                    source: orcadIntersheetReferences_1.OrCadIntersheetReferenceSource.Computed,
	                });
	            }
	            return;
	        }
	        if (computed === undefined) {
	            uncorroboratedCount++;
	            references.push(storedReference);
	            return;
	        }
	        references.push(storedReference);
	        if (computed !== storedReference.value) {
	            mismatches.push(`${objectKind === orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port ? 'port' : 'off-page connector'} ${objectDbId} of ${host.sourceFile} stores "${storedReference.value}" where the project computes "${computed}"`);
	        }
	    };
	    for (const host of pages) {
	        const parentPageNumber = parentPageNumbers.get(host.designId);
	        const pageIsUncompiled = uncompiledPages.has(host.page);
	        const referenceForm = referenceFormBySourceFile.get(host.sourceFile) ?? IntersheetReferenceForm.PageNumber;
	        const pageUsesGridForm = referenceForm === IntersheetReferenceForm.Grid;
	        const pageFormIsAmbiguous = referenceForm === IntersheetReferenceForm.Ambiguous;
	        const pageCorroborationUnsettled = pageCarriesOnlyGlobalSymbols(host);
	        for (const connector of host.page.OffPageConnector ?? []) {
	            const kind = orcadIntersheetReferences_1.OrCadIntersheetObjectKind.OffPageConnector;
	            const isStored = stored.has(intersheetReferenceLabelKey(host.designId, kind, connector.DbId));
	            const refuse = (count) => {
	                if (!isStored) {
	                    count();
	                }
	                settle(host, kind, connector.DbId, undefined);
	            };
	            if (pageUsesGridForm) {
	                refuse(() => tally.gridForm++);
	                continue;
	            }
	            if (pageFormIsAmbiguous) {
	                refuse(() => tally.ambiguousForm++);
	                continue;
	            }
	            const name = normalizedIrefNetName(connector.Name);
	            if (!name) {
	                refuse(() => tally.unnamed++);
	                continue;
	            }
	            if (pageIsUncompiled) {
	                refuse(() => tally.uncompiled++);
	                continue;
	            }
	            const flatNetIds = connectorFlatNets.get(host.designId)?.get(connector.DbId);
	            if (!flatNetIds || flatNetIds.size === 0) {
	                refuse(() => tally.unbound++);
	                continue;
	            }
	            const unresolved = unresolvedReason(flatNetIds);
	            if (unresolved !== undefined) {
	                unresolvedReasons.add(unresolved);
	                refuse(() => tally.unresolvedFlatNet++);
	                continue;
	            }
	            const unverified = isUnverifiedIntersheetReference(host, name);
	            if (unverified && pageCorroborationUnsettled) {
	                refuse(() => tally.globalOnlyPage++);
	                continue;
	            }
	            const referenced = flatNetPageNumbers(flatNetIds, pages).filter((pageNumber) => pageNumber !== host.projectNumber ||
	                hostPageRepeatsConnector(host, connector.DbId, flatNetIds, connectorFlatNets));
	            const value = formatIrefValue(referenced, unverified);
	            if (value === undefined && !isStored) {
	                tally.unreferenced++;
	            }
	            settle(host, kind, connector.DbId, value);
	        }
	        for (const port of host.page.Port ?? []) {
	            const kind = orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port;
	            const isStored = stored.has(intersheetReferenceLabelKey(host.designId, kind, port.DbId));
	            const refuse = (count) => {
	                if (!isStored) {
	                    count();
	                }
	                settle(host, kind, port.DbId, undefined);
	            };
	            if (pageUsesGridForm) {
	                refuse(() => tally.gridForm++);
	                continue;
	            }
	            if (pageFormIsAmbiguous) {
	                refuse(() => tally.ambiguousForm++);
	                continue;
	            }
	            if (pageIsUncompiled || uncompiledPorts.get(host.page)?.has(port.DbId) === true) {
	                refuse(() => tally.uncompiled++);
	                continue;
	            }
	            const flatNetIds = portFlatNets.get(host.designId)?.get(port.DbId);
	            if (!flatNetIds || flatNetIds.size === 0) {
	                refuse(() => tally.unbound++);
	                continue;
	            }
	            const unresolved = unresolvedReason(flatNetIds);
	            if (unresolved !== undefined) {
	                unresolvedReasons.add(unresolved);
	                refuse(() => tally.unresolvedFlatNet++);
	                continue;
	            }
	            if (parentPageNumber === undefined) {
	                refuse(() => tally.parentless++);
	                continue;
	            }
	            const name = normalizedIrefNetName(port.Name);
	            const unverified = name !== undefined && isUnverifiedIntersheetReference(host, name);
	            if (unverified && pageCorroborationUnsettled) {
	                refuse(() => tally.globalOnlyPage++);
	                continue;
	            }
	            const value = formatIrefValue(componentPinPageNumbers(flatNetIds, pages).filter((pageNumber) => pageNumber !== host.projectNumber), unverified, parentPageNumber);
	            if (value === undefined && !isStored) {
	                tally.unreferenced++;
	            }
	            settle(host, kind, port.DbId, value);
	        }
	    }
	    for (const [key, reference] of stored) {
	        if (!claimed.has(key)) {
	            uncorroboratedCount++;
	            references.push(reference);
	        }
	    }
	    const results = intersheetReferenceResults(tally, unresolvedReasons);
	    if (reusedPageConnectors > 0) {
	        results.push({
	            type: OrCadStoredIntersheetReferenceResultType,
	            message: `The stored intersheet reference was passed over on ${reusedPageConnectors} connector(s) of a page the project places more than once, because the value belongs to one placement and only the page's own record is decoded.`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	        });
	    }
	    if (uncorroboratedCount > 0) {
	        results.push({
	            type: OrCadStoredIntersheetReferenceResultType,
	            message: `The design file's own intersheet reference was emitted unchecked on ${uncorroboratedCount} connector(s): the project's pages and nets settle no value of their own for them.`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	        });
	    }
	    if (mismatches.length > 0) {
	        results.push({
	            type: OrCadIntersheetReferenceMismatchResultType,
	            message: `The design file's own intersheet reference was emitted on ${mismatches.length} connector(s) the project computes differently, because the file's value is the decoded one: ${mismatches.sort().slice(0, MaxReportedIrefMismatches).join('; ')}.`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	        });
	    }
	    return {
	        references,
	        results,
	        pagesByDesignId: new Map(pages.map((page) => [page.designId, page.page])),
	    };
	}
	function intersheetReferenceLabelKey(designId, kind, dbId) {
	    return JSON.stringify([designId, kind, dbId]);
	}
	function intersheetReferenceLabels(references, pagesByDesignId) {
	    const valuesByObject = new Map(references.map((reference) => [
	        intersheetReferenceLabelKey(reference.designId, reference.objectKind, reference.objectDbId),
	        reference.value,
	    ]));
	    const labels = new Map();
	    const claim = (designId, kind, dbId, displayProperties) => {
	        const key = intersheetReferenceLabelKey(designId, kind, dbId);
	        const value = valuesByObject.get(key);
	        const displayProperty = displayProperties?.find((property) => property.Name === IntersheetReferencePropertyName);
	        if (value !== undefined && displayProperty) {
	            labels.set(key, { displayProperty, value });
	        }
	    };
	    for (const reference of references) {
	        const page = pagesByDesignId.get(reference.designId);
	        if (!page || reference.source !== orcadIntersheetReferences_1.OrCadIntersheetReferenceSource.Computed) {
	            continue;
	        }
	        if (reference.objectKind === orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port) {
	            const port = (page.Port ?? []).find((candidate) => candidate.DbId === reference.objectDbId);
	            claim(reference.designId, reference.objectKind, reference.objectDbId, port?.PortDisplayProp);
	            continue;
	        }
	        const connector = (page.OffPageConnector ?? []).find((candidate) => candidate.DbId === reference.objectDbId);
	        claim(reference.designId, reference.objectKind, reference.objectDbId, connector?.OffPageConnectorDisplayProp);
	    }
	    return labels;
	}
	function drawIntersheetReferenceLabels(project, labels) {
	    if (labels.size === 0) {
	        return;
	    }
	    for (const design of Object.values(project.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        for (const layerDrawings of Object.values(design.drawings)) {
	            for (const drawing of layerDrawings) {
	                if (drawing.type !== 'group') {
	                    continue;
	                }
	                const kind = drawing.metadata?.[OrcadDsnSymbolUtils_1.OrCadCrossReferenceKindMetadataKey];
	                const dbId = drawing.metadata?.[OrcadDsnSymbolUtils_1.OrCadCrossReferenceDbIdMetadataKey];
	                if (typeof kind !== 'string' || typeof dbId !== 'number') {
	                    continue;
	                }
	                const label = labels.get(intersheetReferenceLabelKey(design.designId, kind, dbId));
	                if (!label) {
	                    continue;
	                }
	                const fontSize = (0, OrcadDsnSymbolUtils_1.scaleFont)(label.displayProperty.Font);
	                drawing.drawings.unshift({
	                    type: 'text',
	                    value: label.value,
	                    pos: [label.displayProperty.LocX, label.displayProperty.LocY + fontSize],
	                    angle: label.displayProperty.Rotation * 90,
	                    justify: [-1, -1],
	                    size: fontSize,
	                    thickness: 1,
	                });
	            }
	        }
	    }
	}
	function intersheetReferenceResults(tally, unresolvedReasons) {
	    const messages = [
	        [tally.unbound, 'connector(s) the transform put on no compiled net, so the pages their net reaches are unknown'],
	        [
	            tally.parentless,
	            'hierarchical port(s) whose schematic folder is reached from no single page, so the parent-page marker could not be placed',
	        ],
	        [tally.unreferenced, 'connector(s) whose net reaches no other page of the project'],
	        [tally.unnamed, 'off-page connector(s) that carry no name'],
	        [
	            tally.gridForm,
	            "connector(s) whose stored value addresses pages by drawing-frame grid zone, which the design's template defines and the design file does not carry",
	        ],
	        [
	            tally.outsideProject,
	            'connector(s) on pages the requested root design does not reach, so they are not part of the numbered project',
	        ],
	        [tally.uncompiled, 'connector(s) on a page or label the transform could not compile'],
	        [
	            tally.ambiguousForm,
	            'connector(s) whose design references values of no one form, so the form its template writes cannot be read',
	        ],
	        [
	            tally.globalOnlyPage,
	            'connector(s) on a page that places no part but does place global power or ground symbols; whether Capture counts a global symbol as corroboration for the unverified marker is not settled by the current fixtures',
	        ],
	        [
	            tally.unresolvedFlatNet,
	            `connector(s) whose flat net the naming rules did not resolve: ${Array.from(unresolvedReasons).sort().join('; ')}`,
	        ],
	    ];
	    const results = [];
	    for (const [count, reason] of messages) {
	        if (count > 0) {
	            results.push({
	                type: OrCadIntersheetReferenceResultType,
	                message: `Intersheet references were left empty on ${count} ${reason}.`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	            });
	        }
	    }
	    return results;
	}
	function crossFileLinkTargets(entries) {
	    const targetsByDesign = new Map();
	    for (const entry of entries) {
	        if (entry.type !== orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic) {
	            continue;
	        }
	        for (const schematic of entry.design.Schematic) {
	            for (const page of schematic.Page) {
	                for (const instance of [...(page.DrawnInst ?? []), ...(page.PartInst ?? [])]) {
	                    if (!instance.ContentsLibName) {
	                        continue;
	                    }
	                    const target = findLogicalSchematicEntry(entries, entry.sourceFile, instance.ContentsLibName, instance.ContentsViewName);
	                    if (!target || target === entry) {
	                        continue;
	                    }
	                    const targets = targetsByDesign.get(entry.design) ?? new Map();
	                    targets.set(String(instance.DbId), {
	                        design: target.design,
	                        viewName: instance.ContentsViewName,
	                        sourceFile: target.sourceFile,
	                    });
	                    targetsByDesign.set(entry.design, targets);
	                }
	            }
	        }
	    }
	    return targetsByDesign;
	}
	const OrCadCrossFileLinkCycleResultType = 'cross-file-link-cycle';
	function withLinkedOccurrences(occurrences, path, targetsByDesign, cycles) {
	    const design = path[path.length - 1]?.design;
	    if (!occurrences || design === undefined) {
	        return occurrences;
	    }
	    const describe = (entry) => `${entry.sourceFile}/${entry.viewName}`;
	    let changed = false;
	    const rebuilt = occurrences.map((occurrence) => {
	        const target = targetsByDesign.get(design)?.get(occurrence.RefInstId);
	        if (target) {
	            const targetEntry = { design: target.design, sourceFile: target.sourceFile, viewName: target.viewName };
	            if (path.some((entry) => entry.sourceFile === target.sourceFile && entry.viewName === target.viewName)) {
	                cycles.add([...path.map(describe), describe(targetEntry)].join(' -> '));
	                return occurrence;
	            }
	            changed = true;
	            const linked = linkedTargetRootChildren(occurrence, target.design, target.viewName);
	            const targetPath = [...path, targetEntry];
	            return {
	                ...occurrence,
	                InstOccurrence: (withLinkedOccurrences(linked, targetPath, targetsByDesign, cycles) ??
	                    linked),
	            };
	        }
	        const rebuiltChildren = withLinkedOccurrences(occurrence.InstOccurrence, path, targetsByDesign, cycles);
	        if (rebuiltChildren === occurrence.InstOccurrence) {
	            return occurrence;
	        }
	        changed = true;
	        return { ...occurrence, InstOccurrence: rebuiltChildren };
	    });
	    return changed ? rebuilt : occurrences;
	}
	function linkDsnCrossFileOccurrences(def, results) {
	    const entries = collectLogicalSchematicEntries(def);
	    if (entries.length <= 1) {
	        return def;
	    }
	    const sourceFileByDesign = new Map(entries.map((entry) => [entry.design, entry.sourceFile]));
	    const cycles = new Set();
	    const targetsByDesign = crossFileLinkTargets(entries);
	    if (targetsByDesign.size === 0) {
	        return def;
	    }
	    const schematics = Object.fromEntries(Object.entries(def.schematics).map(([key, schematic]) => {
	        if (!isValidOrCADLogicalSchematic(schematic)) {
	            return [key, schematic];
	        }
	        const metadata = schematic.validationResult.data.metadata;
	        const design = metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
	        const rebuilt = withLinkedOccurrences(design.Occurrences.InstOccurrence, [{ design, sourceFile: sourceFileByDesign.get(design) ?? key, viewName: design.RootName }], targetsByDesign, cycles);
	        if (rebuilt === design.Occurrences.InstOccurrence) {
	            return [key, schematic];
	        }
	        const linkedDesign = {
	            ...design,
	            Occurrences: { InstOccurrence: (rebuilt ?? []) },
	        };
	        return [
	            key,
	            {
	                ...schematic,
	                validationResult: {
	                    ...schematic.validationResult,
	                    data: {
	                        ...schematic.validationResult.data,
	                        metadata: { ...metadata, [orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema]: linkedDesign },
	                    },
	                },
	            },
	        ];
	    }));
	    if (results && cycles.size > 0) {
	        results.push({
	            type: OrCadCrossFileLinkCycleResultType,
	            message: `Block placements reference one another in a loop, so the contents of the design each loop closes on are left where the loop begins: ${Array.from(cycles).sort().join('; ')}.`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        });
	    }
	    return { ...def, schematics };
	}
	function unresolvedReferenceProcessingResult(reference) {
	    return {
	        type: 'orcad-referenced-project-file-unresolved',
	        message: `Referenced OrCAD schematic could not be resolved: ${reference.referencedFile} (${reference.reason})`,
	        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        referencedFrom: reference.referencedFrom,
	        reference: reference.referencedFile,
	        normalizedPath: reference.normalizedPath,
	        reason: reference.reason,
	        candidates: reference.candidates,
	    };
	}
	function collectCfbDecodeDiagnostics(def) {
	    const results = [];
	    for (const schematic of Object.values(def.schematics)) {
	        if (!isValidOrCADLogicalSchematic(schematic)) {
	            continue;
	        }
	        const diagnostics = schematic.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchemaDiagnostics];
	        if (Array.isArray(diagnostics)) {
	            for (const diagnostic of diagnostics) {
	                results.push(diagnostic);
	            }
	        }
	    }
	    return results;
	}
	function viewResolvedReferenceProcessingResult(reference) {
	    return {
	        type: 'orcad-referenced-project-file-resolved-via-view',
	        message: `Stale OrCAD schematic path resolved by view ${reference.viewName}: ${reference.referencedFile} -> ${reference.resolvedFile}`,
	        severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
	        referencedFrom: reference.referencedFrom,
	        reference: reference.referencedFile,
	        normalizedPath: reference.normalizedPath,
	        resolvedFile: reference.resolvedFile,
	        viewName: reference.viewName,
	        matchKind: reference.matchKind,
	    };
	}
	function parseProject(def, initialSettings = {}) {
	    const settings = { ...getDefaultSettings(), ...initialSettings };
	    if (def.type !== 'orcad') {
	        throw new Error(`Attempted to parse non-OrCAD project def: ${def.type}`);
	    }
	    if (settings.legacyParser) {
	        return { projectModel: parseProjectLegacy(def, settings), extractedImages: [], intersheetReferences: [] };
	    }
	    const crossFileLinkResults = [];
	    const logicalDef = linkDsnCrossFileOccurrences(preferDsnLogicalSchemas(def), crossFileLinkResults);
	    const referenceAudit = auditReferencedProjectFiles(logicalDef);
	    const processingResults = referenceAudit.unresolvedReferences.map(unresolvedReferenceProcessingResult);
	    processingResults.push(...crossFileLinkResults);
	    processingResults.push(...referenceAudit.viewResolvedReferences.map(viewResolvedReferenceProcessingResult));
	    for (const diagnostic of collectCfbDecodeDiagnostics(logicalDef)) {
	        processingResults.push(diagnostic);
	    }
	    if (!referenceAudit.hasValidSchematics) {
	        throw new Error('No valid OrCAD Capture schematic XML files found in project');
	    }
	    const combobulationDebugLayerId = settings.combobulationDebug ? orcadLayers_1.OrCadSchLayers.find((l) => l.type === sigma_engine_shared_1.SchLayerType.Other).id : undefined;
	    const transformResult = (0, OrcadDsnOrchestratedTransformer_1.transformDsnProject)(logicalDef, {
	        ...(combobulationDebugLayerId !== undefined ? { combobulationDebugLayerId } : {}),
	        ...(settings.requestedSourceFile !== undefined ? { requestedSourceFile: settings.requestedSourceFile } : {}),
	        netsMustHaveWireOrMultipleConnections: true,
	        ...(initialSettings.transformOpts ?? {}),
	    });
	    if (transformResult.projectModels.length === 0) {
	        throw new Error('Orchestrated transformer produced no project models');
	    }
	    if (transformResult.rootSourceFiles.length > 1) {
	        processingResults.push({
	            type: 'orcad-parsing',
	            message: `Multiple independent OrCAD roots produced project models (${transformResult.rootSourceFiles.length}): ${transformResult.rootSourceFiles.join(', ')}. Deterministically selected ${transformResult.rootSourceFiles[0]}; request a specific DSN source file to select another root.`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        });
	    }
	    const project = transformResult.projectModels[0];
	    const intersheetReferences = computeProjectIntersheetReferences(logicalDef, transformResult, settings.requestedSourceFile);
	    processingResults.push(...intersheetReferences.results);
	    drawIntersheetReferenceLabels(project, intersheetReferenceLabels(intersheetReferences.references, intersheetReferences.pagesByDesignId));
	    if (settings.mergeDisjointComponents) {
	        (0, mergeDisjointComponentsUtil_1.mergeDisjointComponents)(project);
	    }
	    applyOnlPlacementPropertiesToDsnProjectModel(project, logicalDef);
	    project.modelVersion = ModelText_1.MODEL_TEXT_MIN_VERSION;
	    for (const result of processingResults) {
	        project.processingResults.innerResults.push(result);
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
	    return {
	        projectModel: project,
	        extractedImages: transformResult.extractedImages,
	        intersheetReferences: intersheetReferences.references,
	    };
	}
	function parseProjectLegacy(def, settings) {
	    const logicalDef = linkDsnCrossFileOccurrences(preferDsnLogicalSchemas(def));
	    const referenceAudit = auditReferencedProjectFiles(logicalDef);
	    if (!referenceAudit.hasValidSchematics) {
	        throw new Error('No valid OrCAD Capture schematic XML files found in project');
	    }
	    const schematicMap = new Map();
	    for (const [, xmlSch] of Object.entries(logicalDef.schematics)) {
	        if (isValidOrCADLogicalSchematic(xmlSch)) {
	            const parsedDesign = xmlSch.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
	            if (!parsedDesign) {
	                throw new Error(`No parsed design found for ${xmlSch.originalFileName}`);
	            }
	            schematicMap.set(xmlSch.originalFileName, parsedDesign);
	        }
	    }
	    const transformer = new orcadDsnTransformer_1.OrCadDSNTransformer(Array.from(schematicMap.values()));
	    const project = transformer.transformToProjectModel();
	    if (settings.mergeDisjointComponents) {
	        (0, mergeDisjointComponentsUtil_1.mergeDisjointComponents)(project);
	    }
	    applyOnlPlacementPropertiesToDsnProjectModel(project, logicalDef);
	    project.processingResults.innerResults.push(...referenceAudit.unresolvedReferences.map(unresolvedReferenceProcessingResult), ...referenceAudit.viewResolvedReferences.map(viewResolvedReferenceProcessingResult));
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
	    return project;
	}
	
	return parseOrCADProject;
}

var orcadNetlistToSchematic = {};

var hasRequiredOrcadNetlistToSchematic;

function requireOrcadNetlistToSchematic () {
	if (hasRequiredOrcadNetlistToSchematic) return orcadNetlistToSchematic;
	hasRequiredOrcadNetlistToSchematic = 1;
	Object.defineProperty(orcadNetlistToSchematic, "__esModule", { value: true });
	orcadNetlistToSchematic.convertOrcadNetlistToSchematic = convertOrcadNetlistToSchematic;
	orcadNetlistToSchematic.getSchematicFromOrcadParseResult = getSchematicFromOrcadParseResult;
	const sigma_engine_shared_1 = requireDist$1();
	function convertOrcadNetlistToSchematic(netlist, designId = 'orcad-netlist') {
	    const warnings = [];
	    const modules = [];
	    const moduleMap = new Map();
	    if (netlist.placement?.components) {
	        for (const component of netlist.placement.components) {
	            for (const place of component.places) {
	                const transform = {
	                    angle: 0,
	                    mirrored: [false, false],
	                };
	                const module = {
	                    ref: place.refdes,
	                    footprintRef: component.name,
	                    componentRef: component.name,
	                    center: [0, 0],
	                    layer: 'F',
	                    transform,
	                    name: place.refdes,
	                    pinNets: {},
	                    drawProperties: {},
	                };
	                modules.push(module);
	                moduleMap.set(place.refdes, module);
	            }
	        }
	    }
	    const nets = {};
	    if (netlist.network?.nets) {
	        for (const net of netlist.network.nets) {
	            const connections = [];
	            for (const pin of net.pins ?? []) {
	                const connection = {
	                    moduleRef: pin.referenceDesignator,
	                    moduleName: pin.referenceDesignator,
	                    pinRef: pin.pin,
	                };
	                connections.push(connection);
	                const module = moduleMap.get(pin.referenceDesignator);
	                if (module) {
	                    module.pinNets[pin.pin] = net.name;
	                }
	                else {
	                    warnings.push(`Net '${net.name}' references unknown module '${pin.referenceDesignator}'`);
	                }
	            }
	            nets[net.name] = {
	                name: net.name,
	                connections,
	            };
	        }
	    }
	    const schematic = {
	        modelType: 'schematic',
	        designId,
	        sourceFile: 'orcad-netlist',
	        sheetIndex: 0,
	        layerSet: 'default',
	        drawings: {},
	        edges_bbox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
	        initial_zoombox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
	        nets,
	        modules,
	        ports: {},
	        metadata: {
	            title: netlist.description?.title ?? 'OrCAD Netlist Import',
	            revision: netlist.description?.revision ?? '',
	            company: netlist.description?.organization ?? '',
	            date: netlist.description?.date ?? new Date().toISOString().split('T')[0],
	        },
	        warnings,
	    };
	    return { schematic, warnings };
	}
	function getSchematicFromOrcadParseResult(parseResult, designId) {
	    if (parseResult.processingResults.severity === sigma_engine_shared_1.BoundaryLevel.Error) {
	        return undefined;
	    }
	    if (!parseResult.netlist.network?.nets?.length) {
	        return undefined;
	    }
	    return convertOrcadNetlistToSchematic(parseResult.netlist, designId);
	}
	
	return orcadNetlistToSchematic;
}

var getOrcadPinNameForRendering = {};

var hasRequiredGetOrcadPinNameForRendering;

function requireGetOrcadPinNameForRendering () {
	if (hasRequiredGetOrcadPinNameForRendering) return getOrcadPinNameForRendering;
	hasRequiredGetOrcadPinNameForRendering = 1;
	Object.defineProperty(getOrcadPinNameForRendering, "__esModule", { value: true });
	getOrcadPinNameForRendering.getOrcadPinNameForRendering = getOrcadPinNameForRendering$1;
	const ModelText = requireModelText();
	function getOrcadPinNameForRendering$1(text, pinStart) {
	    const display = ModelText.resolveDisplayText({ raw: text.value.toString(), dialects: [ModelText.TextDialect.Orcad] });
	    if (display.transforms.length === 0) {
	        return text;
	    }
	    const renderingText = {
	        ...text,
	        value: display.text,
	        textTransforms: [...(text.textTransforms ?? []), ...display.transforms],
	    };
	    const rawHalfWidth = (text.value.toString().length * text.size) / 2;
	    const displayHalfWidth = (renderingText.value.toString().length * text.size) / 2;
	    const [x, y] = text.pos;
	    const [pinX, pinY] = pinStart;
	    const horizontalOffset = x - pinX;
	    const verticalOffset = y - pinY;
	    const positionTolerance = Math.max(1, text.size) * 1e-6;
	    if (Math.abs(Math.abs(horizontalOffset) - rawHalfWidth) <= positionTolerance &&
	        Math.abs(verticalOffset) <= positionTolerance) {
	        return { ...renderingText, pos: [pinX + Math.sign(horizontalOffset) * displayHalfWidth, y] };
	    }
	    if (Math.abs(Math.abs(verticalOffset) - rawHalfWidth) <= positionTolerance &&
	        Math.abs(horizontalOffset) <= positionTolerance) {
	        return { ...renderingText, pos: [x, pinY + Math.sign(verticalOffset) * displayHalfWidth] };
	    }
	    return renderingText;
	}
	
	return getOrcadPinNameForRendering;
}

var onl = {};

var ONLTypes = {};

var hasRequiredONLTypes;

function requireONLTypes () {
	if (hasRequiredONLTypes) return ONLTypes;
	hasRequiredONLTypes = 1;
	Object.defineProperty(ONLTypes, "__esModule", { value: true });
	
	return ONLTypes;
}

var hasRequiredOnl;

function requireOnl () {
	if (hasRequiredOnl) return onl;
	hasRequiredOnl = 1;
	(function (exports) {
		var __createBinding = (onl && onl.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (onl && onl.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireONLTypes(), exports);
		__exportStar(requireONLParser(), exports);
		
	} (onl));
	return onl;
}

var hasRequiredOrCAD;

function requireOrCAD () {
	if (hasRequiredOrCAD) return OrCAD;
	hasRequiredOrCAD = 1;
	(function (exports) {
		var __createBinding = (OrCAD && OrCAD.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (OrCAD && OrCAD.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireOrcadValidation(), exports);
		__exportStar(requireOrcadRequiredFiles(), exports);
		__exportStar(requireParseOrCADProject(), exports);
		__exportStar(requireOrcadNetlistToSchematic(), exports);
		__exportStar(requireOrcadTextCodec(), exports);
		__exportStar(requireGetOrcadPinNameForRendering(), exports);
		__exportStar(requireDsncfb(), exports);
		__exportStar(requireOnl(), exports);
		
	} (OrCAD));
	return OrCAD;
}

var Allegro = {};

var parseAllegroProject = {};

var allegroValidation = {};

var cpmParser = {};

var hasRequiredCpmParser;

function requireCpmParser () {
	if (hasRequiredCpmParser) return cpmParser;
	hasRequiredCpmParser = 1;
	Object.defineProperty(cpmParser, "__esModule", { value: true });
	cpmParser.parseCpm = parseCpm;
	const SECTION_START = /^START_([A-Z][A-Z0-9_]*)\s*$/;
	const SECTION_END = /^END_([A-Z][A-Z0-9_]*)\s*$/;
	const COMMENT_LINE = /^\s*\{[^}]*\}\s*$/;
	const QUOTED_VALUE_GLOBAL = /'([^']*)'/g;
	function parseCpm(content) {
	    const sections = new Map();
	    let current = null;
	    for (const rawLine of content.split(/\r?\n/)) {
	        const line = rawLine.trim();
	        if (line.length === 0 || COMMENT_LINE.test(line)) {
	            continue;
	        }
	        const startMatch = SECTION_START.exec(line);
	        if (startMatch !== null) {
	            current = { name: startMatch[1], entries: [] };
	            sections.set(current.name, current);
	            continue;
	        }
	        const endMatch = SECTION_END.exec(line);
	        if (endMatch !== null) {
	            current = null;
	            continue;
	        }
	        if (current === null) {
	            continue;
	        }
	        const entry = parseEntryLine(line);
	        if (entry !== null) {
	            current.entries.push(entry);
	        }
	    }
	    return {
	        sections,
	        designName: getSingleValue(sections, 'GLOBAL', 'design_name'),
	        designLibrary: getSingleValue(sections, 'GLOBAL', 'design_library'),
	        libraries: getMultiValue(sections, 'GLOBAL', 'library'),
	        cpmVersion: getSingleValue(sections, 'GLOBAL', 'cpm_version'),
	        designDb: getSingleValue(sections, 'CANVAS', 'design_db'),
	    };
	}
	function parseEntryLine(line) {
	    const firstSpace = line.search(/\s/);
	    const key = firstSpace === -1 ? line : line.slice(0, firstSpace);
	    if (key.length === 0) {
	        return null;
	    }
	    const rest = firstSpace === -1 ? '' : line.slice(firstSpace);
	    const values = [];
	    for (const match of rest.matchAll(QUOTED_VALUE_GLOBAL)) {
	        values.push(match[1]);
	    }
	    return { key, values };
	}
	function getSingleValue(sections, sectionName, key) {
	    const section = sections.get(sectionName);
	    if (section === undefined) {
	        return '';
	    }
	    for (const entry of section.entries) {
	        if (entry.key === key) {
	            return entry.values[0] ?? '';
	        }
	    }
	    return '';
	}
	function getMultiValue(sections, sectionName, key) {
	    const section = sections.get(sectionName);
	    if (section === undefined) {
	        return [];
	    }
	    for (const entry of section.entries) {
	        if (entry.key === key) {
	            return [...entry.values];
	        }
	    }
	    return [];
	}
	
	return cpmParser;
}

var cdsLibParser = {};

var hasRequiredCdsLibParser;

function requireCdsLibParser () {
	if (hasRequiredCdsLibParser) return cdsLibParser;
	hasRequiredCdsLibParser = 1;
	Object.defineProperty(cdsLibParser, "__esModule", { value: true });
	cdsLibParser.parseCdsLib = parseCdsLib;
	const COMMENT_MARKER = '--';
	function parseCdsLib(content) {
	    const defines = new Map();
	    const includes = [];
	    for (const rawLine of content.split(/\r?\n/)) {
	        const line = stripComment(rawLine).trim();
	        if (line.length === 0) {
	            continue;
	        }
	        const tokens = line.split(/\s+/);
	        const directive = tokens[0].toUpperCase();
	        if (directive === 'DEFINE' && tokens.length >= 3) {
	            const libName = tokens[1];
	            const path = tokens.slice(2).join(' ');
	            defines.set(libName, path);
	            continue;
	        }
	        if ((directive === 'INCLUDE' || directive === 'SOFTINCLUDE') && tokens.length >= 2) {
	            includes.push({
	                type: directive,
	                path: tokens.slice(1).join(' '),
	            });
	            continue;
	        }
	    }
	    return { defines, includes };
	}
	function stripComment(line) {
	    const idx = line.indexOf(COMMENT_MARKER);
	    if (idx < 0) {
	        return line;
	    }
	    const before = idx === 0 ? '' : line[idx - 1];
	    if (idx === 0 || before === ' ' || before === '\t') {
	        return line.slice(0, idx);
	    }
	    return line;
	}
	
	return cdsLibParser;
}

var AllegroNetlistParser = {};

var AllegroTypes = {};

var hasRequiredAllegroTypes;

function requireAllegroTypes () {
	if (hasRequiredAllegroTypes) return AllegroTypes;
	hasRequiredAllegroTypes = 1;
	Object.defineProperty(AllegroTypes, "__esModule", { value: true });
	AllegroTypes.AllegroNetlistConversionMessageType = AllegroTypes.AllegroParsingMessageType = void 0;
	var AllegroParsingMessageType;
	(function (AllegroParsingMessageType) {
	    AllegroParsingMessageType["UnknownProperty"] = "allegro-unknown-property";
	    AllegroParsingMessageType["ParsingWarning"] = "allegro-parsing-warning";
	})(AllegroParsingMessageType || (AllegroTypes.AllegroParsingMessageType = AllegroParsingMessageType = {}));
	var AllegroNetlistConversionMessageType;
	(function (AllegroNetlistConversionMessageType) {
	    AllegroNetlistConversionMessageType["DuplicateNet"] = "allegro-netlist-conversion-duplicate-net";
	})(AllegroNetlistConversionMessageType || (AllegroTypes.AllegroNetlistConversionMessageType = AllegroNetlistConversionMessageType = {}));
	
	return AllegroTypes;
}

var hasRequiredAllegroNetlistParser;

function requireAllegroNetlistParser () {
	if (hasRequiredAllegroNetlistParser) return AllegroNetlistParser;
	hasRequiredAllegroNetlistParser = 1;
	Object.defineProperty(AllegroNetlistParser, "__esModule", { value: true });
	AllegroNetlistParser.isAllegroExpandedNetlist = isAllegroExpandedNetlist;
	AllegroNetlistParser.parseAllegroNetlist = parseAllegroNetlist;
	const sigma_engine_shared_1 = requireDist$1();
	const AllegroTypes_1 = requireAllegroTypes();
	const EXPANDED_NETLIST_HEADER = 'FILE_TYPE = EXPANDEDNETLIST';
	function isAllegroExpandedNetlist(content) {
	    const firstLine = content.split(/\r?\n/, 1)[0].trim();
	    return firstLine.startsWith(EXPANDED_NETLIST_HEADER);
	}
	function parseAllegroNetlist(content) {
	    const warnings = [];
	    const lines = content.split(/\r?\n/);
	    const netlist = {
	        nets: [],
	    };
	    let i = 0;
	    if (i < lines.length) {
	        const headerLine = lines[i].trim();
	        if (!headerLine.startsWith(EXPANDED_NETLIST_HEADER)) {
	            warnings.push({
	                type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
	                message: `Expected FILE_TYPE = EXPANDEDNETLIST header, found: ${headerLine}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	        }
	        i++;
	    }
	    if (i < lines.length) {
	        const commentLine = lines[i].trim();
	        if (commentLine.startsWith('{') && commentLine.endsWith('}')) {
	            netlist.headerComment = commentLine.slice(1, -1).trim();
	            i++;
	        }
	    }
	    let currentNet;
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line || line === 'END.') {
	            i++;
	            continue;
	        }
	        if (line === 'NET_NAME') {
	            currentNet = parseNetNameBlock();
	            if (currentNet) {
	                netlist.nets.push(currentNet);
	            }
	            i++;
	            i = collectNetProperties(lines, i, currentNet);
	            continue;
	        }
	        if (line.startsWith('NODE_NAME')) {
	            const node = parseNodeNameBlock(lines, i, warnings);
	            if (node && currentNet) {
	                currentNet.nodes.push(node);
	            }
	            else if (node && !currentNet) {
	                warnings.push({
	                    type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
	                    message: `NODE_NAME "${node.refdes} ${node.pin}" found outside a NET_NAME block`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                });
	            }
	            i++;
	            i = collectNodeProperties(lines, i, node);
	            continue;
	        }
	        i++;
	    }
	    const processingResults = {
	        type: 'allegro-parsing',
	        message: warnings.length > 0 ?
	            `Allegro netlist parsing completed with ${warnings.length} warning(s)`
	            : 'Allegro netlist parsing completed',
	        innerResults: warnings,
	        severity: warnings.length > 0 ? sigma_engine_shared_1.BoundaryLevel.Warning : sigma_engine_shared_1.BoundaryLevel.Recommendation,
	    };
	    return { netlist, processingResults };
	}
	function parseNetNameBlock(lines, _i, warnings) {
	    return {
	        name: '',
	        properties: {},
	        nodes: [],
	    };
	}
	function collectNetProperties(lines, i, net, warnings) {
	    if (!net) {
	        return i;
	    }
	    let gotName = false;
	    let propertyBuffer = '';
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line) {
	            i++;
	            continue;
	        }
	        if (line === 'NET_NAME' || line.startsWith('NODE_NAME') || line === 'END.') {
	            break;
	        }
	        if (!gotName) {
	            net.name = stripQuotes(line);
	            gotName = true;
	            i++;
	            continue;
	        }
	        if (!net.instancePath && line.startsWith("'@")) {
	            net.instancePath = stripQuotes(line.replace(/:$/, ''));
	            i++;
	            continue;
	        }
	        propertyBuffer += ' ' + line;
	        if (line.endsWith(';')) {
	            parsePropertyString(propertyBuffer, net.properties);
	            propertyBuffer = '';
	        }
	        i++;
	    }
	    if (propertyBuffer.trim()) {
	        parsePropertyString(propertyBuffer, net.properties);
	    }
	    if (net.properties['PHYS_NET_NAME']) {
	        net.name = net.properties['PHYS_NET_NAME'];
	    }
	    return i;
	}
	function parseNodeNameBlock(lines, i, warnings) {
	    const line = lines[i].trim();
	    const match = line.match(/^NODE_NAME\s+(\S+)\s+(\S+)$/);
	    if (!match) {
	        warnings.push({
	            type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
	            message: `Invalid NODE_NAME line: ${line}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        });
	        return undefined;
	    }
	    return {
	        refdes: match[1],
	        pin: match[2],
	        properties: {},
	    };
	}
	function collectNodeProperties(lines, i, node, warnings) {
	    if (!node) {
	        return i;
	    }
	    let propertyBuffer = '';
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line) {
	            i++;
	            continue;
	        }
	        if (line === 'NET_NAME' || line.startsWith('NODE_NAME') || line === 'END.') {
	            break;
	        }
	        if (!node.instancePath && line.startsWith("'@")) {
	            node.instancePath = stripQuotes(line.replace(/:$/, ''));
	            i++;
	            continue;
	        }
	        propertyBuffer += ' ' + line;
	        if (line.endsWith(';')) {
	            parseNodePropertyString(propertyBuffer, node);
	            propertyBuffer = '';
	        }
	        i++;
	    }
	    if (propertyBuffer.trim()) {
	        parseNodePropertyString(propertyBuffer, node);
	    }
	    return i;
	}
	function parsePropertyString(raw, target) {
	    const cleaned = raw.trim().replace(/;$/, '');
	    const pairs = splitProperties(cleaned);
	    for (const pair of pairs) {
	        const trimmed = pair.trim();
	        if (!trimmed) {
	            continue;
	        }
	        const eqIndex = trimmed.indexOf('=');
	        if (eqIndex === -1) {
	            continue;
	        }
	        const key = trimmed.substring(0, eqIndex).trim();
	        const value = stripQuotes(trimmed.substring(eqIndex + 1).trim());
	        target[key] = value;
	    }
	}
	function parseNodePropertyString(raw, node) {
	    const cleaned = raw.trim().replace(/;$/, '');
	    const pinNameMatch = cleaned.match(/^'([^']*)':\s*(.*)/s);
	    if (pinNameMatch) {
	        node.pinName = pinNameMatch[1];
	        parsePropertyString(pinNameMatch[2], node.properties);
	    }
	    else {
	        parsePropertyString(cleaned, node.properties);
	    }
	}
	function splitProperties(str) {
	    const results = [];
	    let current = '';
	    let inQuote = false;
	    for (let i = 0; i < str.length; i++) {
	        const ch = str[i];
	        if (ch === "'") {
	            inQuote = !inQuote;
	        }
	        if (ch === ',' && !inQuote) {
	            results.push(current);
	            current = '';
	        }
	        else {
	            current += ch;
	        }
	    }
	    if (current.trim()) {
	        results.push(current);
	    }
	    return results;
	}
	function stripQuotes(str) {
	    const trimmed = str.trim();
	    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
	        return trimmed.slice(1, -1);
	    }
	    return trimmed;
	}
	
	return AllegroNetlistParser;
}

var AllegroPartListParser = {};

var hasRequiredAllegroPartListParser;

function requireAllegroPartListParser () {
	if (hasRequiredAllegroPartListParser) return AllegroPartListParser;
	hasRequiredAllegroPartListParser = 1;
	Object.defineProperty(AllegroPartListParser, "__esModule", { value: true });
	AllegroPartListParser.isAllegroExpandedPartList = isAllegroExpandedPartList;
	AllegroPartListParser.parseAllegroPartList = parseAllegroPartList;
	const BoundaryLevel_1 = requireBoundaryLevel();
	const AllegroTypes_1 = requireAllegroTypes();
	const EXPANDED_PARTLIST_HEADER = 'FILE_TYPE=EXPANDEDPARTLIST';
	function isAllegroExpandedPartList(content) {
	    const firstLine = content.split(/\r?\n/, 1)[0].trim();
	    return firstLine.startsWith(EXPANDED_PARTLIST_HEADER);
	}
	function parseAllegroPartList(content) {
	    const warnings = [];
	    const lines = content.split(/\r?\n/);
	    const partList = {
	        directives: {},
	        parts: [],
	    };
	    let i = 0;
	    if (i < lines.length) {
	        const headerLine = lines[i].trim();
	        if (!headerLine.startsWith(EXPANDED_PARTLIST_HEADER)) {
	            warnings.push({
	                type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
	                message: `Expected FILE_TYPE=EXPANDEDPARTLIST header, found: ${headerLine}`,
	                severity: BoundaryLevel_1.BoundaryLevel.Warning,
	            });
	        }
	        i++;
	    }
	    if (i < lines.length) {
	        const commentLine = lines[i].trim();
	        if (commentLine.startsWith('{') && commentLine.endsWith('}')) {
	            partList.headerComment = commentLine.slice(1, -1).trim();
	            i++;
	        }
	    }
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line) {
	            i++;
	            continue;
	        }
	        if (line === 'DIRECTIVES') {
	            i++;
	            i = parseDirectives(lines, i, partList.directives);
	            continue;
	        }
	        break;
	    }
	    let currentPart;
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line || line === 'END.') {
	            i++;
	            continue;
	        }
	        if (line === 'PART_NAME') {
	            i++;
	            currentPart = parsePartNameBlock(lines, i, warnings);
	            if (currentPart) {
	                partList.parts.push(currentPart);
	                i = skipPartNameProperties(lines, i);
	            }
	            continue;
	        }
	        if (line.startsWith('SECTION_NUMBER') && currentPart) {
	            i++;
	            i = collectSectionProperties(lines, i, currentPart);
	            continue;
	        }
	        i++;
	    }
	    const processingResults = {
	        type: 'allegro-parsing',
	        message: warnings.length > 0 ?
	            `Allegro part list parsing completed with ${warnings.length} warning(s)`
	            : 'Allegro part list parsing completed',
	        innerResults: warnings,
	        severity: warnings.length > 0 ? BoundaryLevel_1.BoundaryLevel.Warning : BoundaryLevel_1.BoundaryLevel.Recommendation,
	    };
	    return { partList, processingResults };
	}
	function parseDirectives(lines, i, directives, warnings) {
	    let propertyBuffer = '';
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (line === 'END_DIRECTIVES;' || line === 'END_DIRECTIVES') {
	            if (propertyBuffer.trim()) {
	                parsePropertyString(propertyBuffer, directives);
	            }
	            i++;
	            break;
	        }
	        propertyBuffer += ' ' + line;
	        if (line.endsWith(';')) {
	            parsePropertyString(propertyBuffer, directives);
	            propertyBuffer = '';
	        }
	        i++;
	    }
	    return i;
	}
	function parsePartNameBlock(lines, i, warnings) {
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line) {
	            i++;
	            continue;
	        }
	        const match = line.match(/^(\S+)\s+'([^']+)':/);
	        if (match) {
	            return {
	                refdes: match[1],
	                partName: match[2],
	                properties: {},
	            };
	        }
	        warnings.push({
	            type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
	            message: `Invalid PART_NAME line: ${line}`,
	            severity: BoundaryLevel_1.BoundaryLevel.Warning,
	        });
	        return undefined;
	    }
	    return undefined;
	}
	function skipPartNameProperties(lines, i, _warnings) {
	    i++;
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line || line === 'PART_NAME' || line.startsWith('SECTION_NUMBER') || line === 'END.') {
	            break;
	        }
	        i++;
	    }
	    return i;
	}
	function collectSectionProperties(lines, i, part, _warnings) {
	    let propertyBuffer = '';
	    while (i < lines.length) {
	        const line = lines[i].trim();
	        if (!line) {
	            i++;
	            continue;
	        }
	        if (line === 'PART_NAME' || line.startsWith('SECTION_NUMBER') || line === 'END.') {
	            break;
	        }
	        if (line.startsWith("'@")) {
	            i++;
	            continue;
	        }
	        propertyBuffer += ' ' + line;
	        if (line.endsWith(';')) {
	            parsePropertyString(propertyBuffer, part.properties);
	            propertyBuffer = '';
	        }
	        i++;
	    }
	    if (propertyBuffer.trim()) {
	        parsePropertyString(propertyBuffer, part.properties);
	    }
	    return i;
	}
	function parsePropertyString(raw, target) {
	    const cleaned = raw.trim().replace(/;$/, '');
	    const pairs = splitProperties(cleaned);
	    for (const pair of pairs) {
	        const trimmed = pair.trim();
	        if (!trimmed) {
	            continue;
	        }
	        const eqIndex = trimmed.indexOf('=');
	        if (eqIndex === -1) {
	            continue;
	        }
	        const key = trimmed.substring(0, eqIndex).trim();
	        const value = stripQuotes(trimmed.substring(eqIndex + 1).trim());
	        target[key] = value;
	    }
	}
	function splitProperties(str) {
	    const results = [];
	    let current = '';
	    let inQuote = false;
	    for (let i = 0; i < str.length; i++) {
	        const ch = str[i];
	        if (ch === "'") {
	            inQuote = !inQuote;
	        }
	        if (ch === ',' && !inQuote) {
	            results.push(current);
	            current = '';
	        }
	        else {
	            current += ch;
	        }
	    }
	    if (current.trim()) {
	        results.push(current);
	    }
	    return results;
	}
	function stripQuotes(str) {
	    const trimmed = str.trim();
	    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
	        return trimmed.slice(1, -1);
	    }
	    return trimmed;
	}
	
	return AllegroPartListParser;
}

var cdszArchive = {};

var hasRequiredCdszArchive;

function requireCdszArchive () {
	if (hasRequiredCdszArchive) return cdszArchive;
	hasRequiredCdszArchive = 1;
	Object.defineProperty(cdszArchive, "__esModule", { value: true });
	cdszArchive.extractCdsz = extractCdsz;
	cdszArchive.findCdszEntryByBasename = findCdszEntryByBasename;
	cdszArchive.readCdszEntryAsText = readCdszEntryAsText;
	const fflate_1 = /*@__PURE__*/ requireBrowser();
	const utf8Decoder = new TextDecoder('utf-8');
	function extractCdsz(data) {
	    const unzipped = (0, fflate_1.unzipSync)(data);
	    const entries = new Map();
	    for (const path of Object.keys(unzipped)) {
	        if (path.endsWith('/')) {
	            continue;
	        }
	        entries.set(path, { path, contents: unzipped[path] });
	    }
	    return { entries };
	}
	function findCdszEntryByBasename(extracted, basename) {
	    const wanted = basename.toLowerCase();
	    for (const entry of extracted.entries.values()) {
	        const path = entry.path.toLowerCase();
	        const slash = path.lastIndexOf('/');
	        const entryBasename = slash >= 0 ? path.slice(slash + 1) : path;
	        if (entryBasename === wanted) {
	            return entry;
	        }
	    }
	    return undefined;
	}
	function readCdszEntryAsText(entry) {
	    return utf8Decoder.decode(entry.contents);
	}
	
	return cdszArchive;
}

var hasRequiredAllegroValidation;

function requireAllegroValidation () {
	if (hasRequiredAllegroValidation) return allegroValidation;
	hasRequiredAllegroValidation = 1;
	Object.defineProperty(allegroValidation, "__esModule", { value: true });
	allegroValidation.AllegroProjectMetadataKey = allegroValidation.AllegroFileType = void 0;
	allegroValidation.getAllegroFileType = getAllegroFileType;
	allegroValidation.validateAllegroFile = validateAllegroFile;
	allegroValidation.detectAllegroProjects = detectAllegroProjects;
	const project_detection_1 = requireProjectDetection();
	const cpmParser_1 = requireCpmParser();
	const cdsLibParser_1 = requireCdsLibParser();
	const AllegroNetlistParser_1 = requireAllegroNetlistParser();
	const AllegroPartListParser_1 = requireAllegroPartListParser();
	const cdszArchive_1 = requireCdszArchive();
	var AllegroFileType;
	(function (AllegroFileType) {
	    AllegroFileType["Cpm"] = "allegro-cpm";
	    AllegroFileType["CdsLib"] = "allegro-cds-lib";
	    AllegroFileType["Sdax"] = "allegro-sdax";
	    AllegroFileType["Xcon"] = "allegro-xcon";
	    AllegroFileType["Ascii"] = "allegro-ascii";
	    AllegroFileType["Css"] = "allegro-css";
	    AllegroFileType["Cdsz"] = "allegro-cdsz";
	    AllegroFileType["ChipsPrt"] = "allegro-chips-prt";
	    AllegroFileType["Style"] = "allegro-style";
	    AllegroFileType["ExpandedNetlist"] = "allegro-expanded-netlist";
	    AllegroFileType["ExpandedPartList"] = "allegro-expanded-partlist";
	    AllegroFileType["Unknown"] = "allegro-unknown";
	})(AllegroFileType || (allegroValidation.AllegroFileType = AllegroFileType = {}));
	var AllegroProjectMetadataKey;
	(function (AllegroProjectMetadataKey) {
	    AllegroProjectMetadataKey["FileType"] = "file-type";
	    AllegroProjectMetadataKey["ParsedCpm"] = "parsed-cpm";
	    AllegroProjectMetadataKey["ParsedCdsLib"] = "parsed-cds-lib";
	    AllegroProjectMetadataKey["ParsedNetlist"] = "parsed-netlist";
	    AllegroProjectMetadataKey["ParsedPartList"] = "parsed-partlist";
	})(AllegroProjectMetadataKey || (allegroValidation.AllegroProjectMetadataKey = AllegroProjectMetadataKey = {}));
	const CDS_LIB_BASENAME = 'cds.lib';
	function fileContentsToString(fileContents) {
	    if (typeof fileContents === 'string') {
	        return fileContents;
	    }
	    return new TextDecoder().decode(fileContents);
	}
	function getAllegroFileType(filename, content) {
	    const lower = filename.toLowerCase();
	    const basename = lower.split('/').pop() ?? lower;
	    if (basename === CDS_LIB_BASENAME) {
	        return AllegroFileType.CdsLib;
	    }
	    const lastDot = basename.lastIndexOf('.');
	    if (lastDot < 0) {
	        return false;
	    }
	    const ext = basename.slice(lastDot);
	    switch (ext) {
	        case '.cpm':
	            return AllegroFileType.Cpm;
	        case '.sdax':
	            return AllegroFileType.Sdax;
	        case '.xcon':
	            return AllegroFileType.Xcon;
	        case '.ascii':
	            return AllegroFileType.Ascii;
	        case '.cdsz':
	            return AllegroFileType.Cdsz;
	        case '.css':
	            return AllegroFileType.Css;
	        case '.style':
	            return AllegroFileType.Style;
	        case '.prt':
	            if (basename === 'chips.prt') {
	                return AllegroFileType.ChipsPrt;
	            }
	            return false;
	        case '.dat':
	            if (content !== undefined) {
	                if ((0, AllegroNetlistParser_1.isAllegroExpandedNetlist)(content)) {
	                    return AllegroFileType.ExpandedNetlist;
	                }
	                if ((0, AllegroPartListParser_1.isAllegroExpandedPartList)(content)) {
	                    return AllegroFileType.ExpandedPartList;
	                }
	                return false;
	            }
	            if (basename === 'pstxnet.dat') {
	                return AllegroFileType.ExpandedNetlist;
	            }
	            if (basename === 'pstxprt.dat') {
	                return AllegroFileType.ExpandedPartList;
	            }
	            return false;
	    }
	    return false;
	}
	function validateAllegroFile(filename, contents) {
	    const type = getAllegroFileType(filename, typeof contents === 'string' ? contents : undefined);
	    if (type === false) {
	        return {
	            isValid: false,
	            errors: [`Not a recognized Allegro X System Capture file: ${filename}`],
	        };
	    }
	    const semanticType = semanticTypeFor(type);
	    const metadata = {
	        [AllegroProjectMetadataKey.FileType]: type,
	    };
	    switch (type) {
	        case AllegroFileType.Cpm: {
	            const parsed = (0, cpmParser_1.parseCpm)(fileContentsToString(contents));
	            if (parsed.designName === '' || parsed.designDb === '') {
	                return {
	                    isValid: false,
	                    errors: [`.cpm is missing required fields (design_name, design_db) in ${filename}`],
	                };
	            }
	            metadata[AllegroProjectMetadataKey.ParsedCpm] = parsed;
	            break;
	        }
	        case AllegroFileType.CdsLib: {
	            const parsed = (0, cdsLibParser_1.parseCdsLib)(fileContentsToString(contents));
	            if (parsed.defines.size === 0 && parsed.includes.length === 0) {
	                return {
	                    isValid: false,
	                    errors: [`cds.lib is empty or unparseable in ${filename}`],
	                };
	            }
	            metadata[AllegroProjectMetadataKey.ParsedCdsLib] = parsed;
	            break;
	        }
	        case AllegroFileType.ExpandedNetlist: {
	            const parseResult = (0, AllegroNetlistParser_1.parseAllegroNetlist)(fileContentsToString(contents));
	            if (!parseResult.netlist.nets.length) {
	                return { isValid: false, errors: ['No nets found in EXPANDEDNETLIST file'] };
	            }
	            metadata[AllegroProjectMetadataKey.ParsedNetlist] = parseResult;
	            metadata['tool'] = 'OrCAD Allegro Packager-XL';
	            metadata['netCount'] = parseResult.netlist.nets.length;
	            metadata['nodeCount'] = parseResult.netlist.nets.reduce((sum, net) => sum + net.nodes.length, 0);
	            if (parseResult.netlist.headerComment) {
	                metadata['headerComment'] = parseResult.netlist.headerComment;
	            }
	            break;
	        }
	        case AllegroFileType.ExpandedPartList: {
	            const parseResult = (0, AllegroPartListParser_1.parseAllegroPartList)(fileContentsToString(contents));
	            if (!parseResult.partList.parts.length) {
	                return { isValid: false, errors: ['No parts found in EXPANDEDPARTLIST file'] };
	            }
	            metadata[AllegroProjectMetadataKey.ParsedPartList] = parseResult;
	            metadata['tool'] = 'OrCAD Allegro Packager-XL';
	            metadata['partCount'] = parseResult.partList.parts.length;
	            if (parseResult.partList.headerComment) {
	                metadata['headerComment'] = parseResult.partList.headerComment;
	            }
	            break;
	        }
	        case AllegroFileType.Cdsz: {
	            if (typeof contents === 'string') {
	                return {
	                    isValid: false,
	                    errors: [`.cdsz archives require binary contents to validate: ${filename}`],
	                };
	            }
	            let extracted;
	            try {
	                extracted = (0, cdszArchive_1.extractCdsz)(contents);
	            }
	            catch (e) {
	                return {
	                    isValid: false,
	                    errors: [`.cdsz archive is not a readable ZIP: ${filename}`, e instanceof Error ? e.message : String(e)],
	                };
	            }
	            const netlistEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extracted, 'pstxnet.dat');
	            if (netlistEntry) {
	                const netlistResult = (0, AllegroNetlistParser_1.parseAllegroNetlist)((0, cdszArchive_1.readCdszEntryAsText)(netlistEntry));
	                if (!netlistResult.netlist.nets.length) {
	                    return { isValid: false, errors: ['No nets found in EXPANDEDNETLIST inside .cdsz'] };
	                }
	                metadata[AllegroProjectMetadataKey.ParsedNetlist] = netlistResult;
	                metadata['tool'] = 'OrCAD Allegro Packager-XL';
	                metadata['netCount'] = netlistResult.netlist.nets.length;
	                metadata['nodeCount'] = netlistResult.netlist.nets.reduce((sum, net) => sum + net.nodes.length, 0);
	                if (netlistResult.netlist.headerComment) {
	                    metadata['headerComment'] = netlistResult.netlist.headerComment;
	                }
	                const partListEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extracted, 'pstxprt.dat');
	                if (partListEntry) {
	                    const partListResult = (0, AllegroPartListParser_1.parseAllegroPartList)((0, cdszArchive_1.readCdszEntryAsText)(partListEntry));
	                    if (partListResult.partList.parts.length) {
	                        metadata[AllegroProjectMetadataKey.ParsedPartList] = partListResult;
	                        metadata['partCount'] = partListResult.partList.parts.length;
	                    }
	                }
	            }
	            break;
	        }
	        case AllegroFileType.Sdax:
	        case AllegroFileType.Xcon:
	        case AllegroFileType.Ascii:
	        case AllegroFileType.Css:
	        case AllegroFileType.ChipsPrt:
	        case AllegroFileType.Style:
	            break;
	        case AllegroFileType.Unknown:
	            break;
	    }
	    return {
	        isValid: true,
	        data: {
	            semanticType,
	            fileFormat: {
	                cadTool: 'allegro',
	                type,
	            },
	            referencedFiles: [],
	            metadata,
	        },
	    };
	}
	function semanticTypeFor(type) {
	    switch (type) {
	        case AllegroFileType.Cpm:
	            return 'project';
	        case AllegroFileType.CdsLib:
	            return 'lib';
	        case AllegroFileType.Sdax:
	            return 'schematic';
	        case AllegroFileType.Xcon:
	            return 'netlist';
	        case AllegroFileType.ExpandedNetlist:
	            return 'netlist';
	        case AllegroFileType.ExpandedPartList:
	            return 'bom';
	        case AllegroFileType.Ascii:
	        case AllegroFileType.Cdsz:
	        case AllegroFileType.Css:
	        case AllegroFileType.ChipsPrt:
	        case AllegroFileType.Style:
	        case AllegroFileType.Unknown:
	            return 'other';
	    }
	}
	const matchCpm = (0, project_detection_1.endsWithExt)('.cpm');
	const matchSdax = (0, project_detection_1.endsWithExt)('.sdax');
	function detectAllegroProjects(files, fileDetails) {
	    return (0, project_detection_1.detectAnchoredProjects)({
	        tool: 'allegro',
	        toolDisplayName: 'Allegro X System Capture',
	        files,
	        fileDetails,
	        primaryAnchorMatch: matchCpm,
	        primaryAnchorLabel: '.cpm',
	        secondaryAnchorMatch: matchSdax,
	        secondaryAnchorLabel: '.sdax',
	    });
	}
	
	return allegroValidation;
}

var AllegroOrchestratedTransformer = {};

var AllegroTransformProjectGenerator = {};

var allegroLayers = {};

var hasRequiredAllegroLayers;

function requireAllegroLayers () {
	if (hasRequiredAllegroLayers) return allegroLayers;
	hasRequiredAllegroLayers = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AllegroSchLayers = exports.AllegroSchLayerMap = exports.ALLEGRO_SCH_LAYERSET = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const { SchLayerType } = sigma_engine_shared_1.DesignModels;
		exports.ALLEGRO_SCH_LAYERSET = 'allegro-sch';
		exports.AllegroSchLayerMap = {
		    [SchLayerType.Label]: { id: 1, type: SchLayerType.Label, name: 'Label' },
		    [SchLayerType.NameLabel]: { id: 2, type: SchLayerType.NameLabel, name: 'NameLabel' },
		    [SchLayerType.ValueLabel]: { id: 3, type: SchLayerType.ValueLabel, name: 'ValueLabel' },
		    [SchLayerType.Pin]: { id: 4, type: SchLayerType.Pin, name: 'Pin' },
		    [SchLayerType.PinName]: { id: 5, type: SchLayerType.PinName, name: 'PinName' },
		    [SchLayerType.PinNumber]: { id: 6, type: SchLayerType.PinNumber, name: 'PinNumber' },
		    [SchLayerType.Symbol]: { id: 7, type: SchLayerType.Symbol, name: 'Symbol' },
		    [SchLayerType.Junction]: { id: 8, type: SchLayerType.Junction, name: 'Junction' },
		    [SchLayerType.BusJunction]: { id: 9, type: SchLayerType.BusJunction, name: 'BusJunction' },
		    [SchLayerType.Wire]: { id: 10, type: SchLayerType.Wire, name: 'Wire' },
		    [SchLayerType.Bus]: { id: 11, type: SchLayerType.Bus, name: 'Bus' },
		    [SchLayerType.Other]: { id: 12, type: SchLayerType.Other, name: 'Other' },
		    [SchLayerType.Background]: { id: 13, type: SchLayerType.Background, name: 'Background Contrast' },
		    [SchLayerType.Image]: { id: 14, type: SchLayerType.Image, name: 'Image' },
		};
		exports.AllegroSchLayers = Object.values(exports.AllegroSchLayerMap);
		
	} (allegroLayers));
	return allegroLayers;
}

var hasRequiredAllegroTransformProjectGenerator;

function requireAllegroTransformProjectGenerator () {
	if (hasRequiredAllegroTransformProjectGenerator) return AllegroTransformProjectGenerator;
	hasRequiredAllegroTransformProjectGenerator = 1;
	Object.defineProperty(AllegroTransformProjectGenerator, "__esModule", { value: true });
	AllegroTransformProjectGenerator.AllegroTransformProjectGenerator = void 0;
	const TransformProject_1 = requireTransformProject();
	const TransformProjectLayerSet_1 = requireTransformProjectLayerSet();
	const allegroLayers_1 = requireAllegroLayers();
	class AllegroTransformProjectGenerator$1 {
	    constructor() {
	        this.runsFor = null;
	    }
	    *generate(_trigger, rootContext) {
	        const designName = rootContext.parsedCpm.designName;
	        const projectName = designName.length > 0 ? designName : 'Allegro Project';
	        const project = new TransformProject_1.TransformProject({ name: projectName, type: 'allegro' }, rootContext);
	        yield project;
	        yield new TransformProjectLayerSet_1.TransformProjectLayerSet(project, {
	            type: 'layerSet',
	            id: allegroLayers_1.ALLEGRO_SCH_LAYERSET,
	            layerSetType: 'schematic',
	            layers: allegroLayers_1.AllegroSchLayers.map((layer) => ({ ...layer, defaultVisible: true })),
	        });
	    }
	}
	AllegroTransformProjectGenerator.AllegroTransformProjectGenerator = AllegroTransformProjectGenerator$1;
	
	return AllegroTransformProjectGenerator;
}

var AllegroTransformSchematicGenerator = {};

var AllegroNetNamingStrategy = {};

var AllegroPropagateXconNameRule = {};

var hasRequiredAllegroPropagateXconNameRule;

function requireAllegroPropagateXconNameRule () {
	if (hasRequiredAllegroPropagateXconNameRule) return AllegroPropagateXconNameRule;
	hasRequiredAllegroPropagateXconNameRule = 1;
	Object.defineProperty(AllegroPropagateXconNameRule, "__esModule", { value: true });
	AllegroPropagateXconNameRule.AllegroPropagateXconNameRule = void 0;
	const NetNamingRules_1 = requireNetNamingRules();
	class AllegroPropagateXconNameRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        for (const [, nets] of group.getNets()) {
	            for (const net of nets) {
	                const name = net.name.valueOf();
	                if (name.length > 0 && !name.startsWith('__UNNAMED_NET_')) {
	                    return name;
	                }
	            }
	        }
	        return undefined;
	    }
	}
	AllegroPropagateXconNameRule.AllegroPropagateXconNameRule = AllegroPropagateXconNameRule$1;
	
	return AllegroPropagateXconNameRule;
}

var AllegroUnconnectedPinNamingRule = {};

var hasRequiredAllegroUnconnectedPinNamingRule;

function requireAllegroUnconnectedPinNamingRule () {
	if (hasRequiredAllegroUnconnectedPinNamingRule) return AllegroUnconnectedPinNamingRule;
	hasRequiredAllegroUnconnectedPinNamingRule = 1;
	Object.defineProperty(AllegroUnconnectedPinNamingRule, "__esModule", { value: true });
	AllegroUnconnectedPinNamingRule.AllegroUnconnectedPinNamingRule = void 0;
	AllegroUnconnectedPinNamingRule.unconnectedPinNetName = unconnectedPinNetName;
	const NetNamingRules_1 = requireNetNamingRules();
	class AllegroUnconnectedPinNamingRule$1 {
	    constructor() {
	        this.type = NetNamingRules_1.NetNamingRuleType.Naming;
	    }
	    apply(group, _hierarchy) {
	        let allPlaceholder = false;
	        for (const [, nets] of group.getNets()) {
	            for (const net of nets) {
	                const name = net.name.valueOf();
	                if (!name.startsWith('__UNNAMED_NET_')) {
	                    return undefined;
	                }
	                allPlaceholder = true;
	            }
	        }
	        if (!allPlaceholder) {
	            return undefined;
	        }
	        const connections = group.getConnections();
	        if (connections.length === 0) {
	            return undefined;
	        }
	        const firstConnection = connections[0];
	        return `unconnected-(${firstConnection.moduleName}-${firstConnection.pinRef})`;
	    }
	}
	AllegroUnconnectedPinNamingRule.AllegroUnconnectedPinNamingRule = AllegroUnconnectedPinNamingRule$1;
	function unconnectedPinNetName(moduleName, pinRef) {
	    return `unconnected-(${moduleName}-${pinRef})`;
	}
	
	return AllegroUnconnectedPinNamingRule;
}

var hasRequiredAllegroNetNamingStrategy;

function requireAllegroNetNamingStrategy () {
	if (hasRequiredAllegroNetNamingStrategy) return AllegroNetNamingStrategy;
	hasRequiredAllegroNetNamingStrategy = 1;
	Object.defineProperty(AllegroNetNamingStrategy, "__esModule", { value: true });
	AllegroNetNamingStrategy.AllegroNetNamingStrategy = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const NetCombobulator_1 = requireNetCombobulator();
	const NetNamingRuleChain_1 = requireNetNamingRuleChain();
	const IndividualGroupingRule_1 = requireIndividualGroupingRule();
	const AllegroPropagateXconNameRule_1 = requireAllegroPropagateXconNameRule();
	const AllegroUnconnectedPinNamingRule_1 = requireAllegroUnconnectedPinNamingRule();
	class AllegroNetNamingStrategy$1 {
	    constructor() {
	        this.ruleChain = new NetNamingRuleChain_1.NetNamingRuleChain();
	        this.ruleChain.add(new IndividualGroupingRule_1.IndividualGroupingRule());
	        this.ruleChain.add(new AllegroPropagateXconNameRule_1.AllegroPropagateXconNameRule());
	        this.ruleChain.add(new AllegroUnconnectedPinNamingRule_1.AllegroUnconnectedPinNamingRule());
	    }
	    transformNetName(netObject, _instance) {
	        return (netObject.label?.strategyMetadata?.xconNetName ??
	            netObject.wire?.strategyMetadata?.xconNetName ??
	            netObject.connection?.connection.appliesNetName?.strategyMetadata?.xconNetName);
	    }
	    canLabelTypeNameNets(_labelType, _instance) {
	        return true;
	    }
	    resolveNetName(net, _instance) {
	        for (const conn of net.connections) {
	            const name = conn.connection.appliesNetName?.strategyMetadata?.xconNetName;
	            if (name !== undefined && name.length > 0) {
	                return name;
	            }
	        }
	        for (const label of net.labels) {
	            const name = label.strategyMetadata?.xconNetName;
	            if (name !== undefined && name.length > 0) {
	                return name;
	            }
	        }
	        for (const wire of net.wires) {
	            const name = wire.strategyMetadata?.xconNetName;
	            if (name !== undefined && name.length > 0) {
	                return name;
	            }
	        }
	        return net.name.valueOf();
	    }
	    resolvePhysicalNetNames(hierarchy) {
	        return this.ruleChain.execute(hierarchy);
	    }
	    createVirtualRootHierarchy(roots) {
	        if (roots.length === 0) {
	            throw new Error('createVirtualRootHierarchy requires at least one root');
	        }
	        const firstChildInstance = roots[0].instance;
	        const virtualInstance = {
	            pathUniqueId: '__allegro_virtual_root__',
	            hierarchyEntry: firstChildInstance.hierarchyEntry,
	            path: [],
	            children: roots.map((c) => c.instance),
	            numInstances: 1,
	            totalSheetInstances: 1,
	            instanceIndex: 0,
	            sheetSymbol: undefined,
	            netRefCtx: new sigma_engine_shared_1.NetRefCtx(),
	        };
	        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this, virtualInstance, { pointRoundingPrecision: 0.1 });
	        return {
	            pathUniqueId: '__allegro_virtual_root__',
	            hierarchyDepth: -1,
	            instance: virtualInstance,
	            combobulator: emptyCombobulator,
	            children: roots,
	        };
	    }
	}
	AllegroNetNamingStrategy.AllegroNetNamingStrategy = AllegroNetNamingStrategy$1;
	
	return AllegroNetNamingStrategy;
}

var hasRequiredAllegroTransformSchematicGenerator;

function requireAllegroTransformSchematicGenerator () {
	if (hasRequiredAllegroTransformSchematicGenerator) return AllegroTransformSchematicGenerator;
	hasRequiredAllegroTransformSchematicGenerator = 1;
	Object.defineProperty(AllegroTransformSchematicGenerator, "__esModule", { value: true });
	AllegroTransformSchematicGenerator.AllegroTransformSchematicGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformProject_1 = requireTransformProject();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformSchematicLayerSet_1 = requireTransformSchematicLayerSet();
	const NetCombobulator_1 = requireNetCombobulator();
	const fonts_1 = requireFonts();
	const allegroLayers_1 = requireAllegroLayers();
	const AllegroNetNamingStrategy_1 = requireAllegroNetNamingStrategy();
	class AllegroTransformSchematicGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformProject_1.TransformProject];
	    }
	    *generate(project, rootContext) {
	        const namingStrategy = new AllegroNetNamingStrategy_1.AllegroNetNamingStrategy();
	        for (let sheetIndex = 0; sheetIndex < rootContext.sheetPages.length; sheetIndex++) {
	            const loaded = rootContext.sheetPages[sheetIndex];
	            const hierarchy = buildHierarchy(loaded);
	            const instance = buildInstance(hierarchy);
	            hierarchy.parents = [];
	            hierarchy.children = [];
	            const combobulator = new NetCombobulator_1.NetCombobulator(instance.netRefCtx, namingStrategy, instance, { pointRoundingPrecision: 0.1 });
	            const schematic = new TransformSchematic_1.TransformSchematic(project, {
	                designId: hierarchy.slug,
	                designName: loaded.sheetName.length > 0 ? loaded.sheetName : loaded.pageFileName,
	                sheetIndex,
	                sourceFile: loaded.sourcePath,
	                font: { default: fonts_1.Font.Newstroke },
	                instance,
	            }, combobulator, project.context);
	            yield schematic;
	            yield buildLayerSet(schematic);
	        }
	    }
	}
	AllegroTransformSchematicGenerator.AllegroTransformSchematicGenerator = AllegroTransformSchematicGenerator$1;
	function buildHierarchy(loaded) {
	    return {
	        name: loaded.sheetName.length > 0 ? loaded.sheetName : loaded.pageFileName,
	        sourceFile: loaded.sourcePath,
	        schematicIndex: 0,
	        hierarchyIndex: 0,
	        children: [],
	        parents: [],
	        schematic: loaded,
	        loaded,
	        slug: hierarchySlug(loaded),
	    };
	}
	function buildInstance(hierarchy) {
	    return {
	        hierarchyEntry: hierarchy,
	        path: [],
	        children: [],
	        pathUniqueId: hierarchy.slug,
	        numInstances: 1,
	        totalSheetInstances: 1,
	        instanceIndex: 0,
	        sheetSymbol: undefined,
	        netRefCtx: new sigma_engine_shared_1.NetRefCtx(),
	    };
	}
	function hierarchySlug(loaded) {
	    if (loaded.sheetName.length > 0) {
	        return `${loaded.sheetName}__${loaded.sheetId}`;
	    }
	    return loaded.pageFileName;
	}
	function buildLayerSet(schematic) {
	    return new TransformSchematicLayerSet_1.TransformSchematicLayerSet(schematic, {
	        type: 'layerSet',
	        id: allegroLayers_1.ALLEGRO_SCH_LAYERSET,
	        layerSetType: 'schematic',
	        layers: allegroLayers_1.AllegroSchLayers.map((layer) => ({ ...layer, defaultVisible: true })),
	    });
	}
	
	return AllegroTransformSchematicGenerator;
}

var AllegroTransformComponentGenerator = {};

var cssParser = {};

var hasRequiredCssParser;

function requireCssParser () {
	if (hasRequiredCssParser) return cssParser;
	hasRequiredCssParser = 1;
	Object.defineProperty(cssParser, "__esModule", { value: true });
	cssParser.CSS_UNIT_TO_MM = void 0;
	cssParser.parseSymbolCss = parseSymbolCss;
	cssParser.CSS_UNIT_TO_MM = 0.129032;
	function parseSymbolCss(content) {
	    const lines = [];
	    const arcs = [];
	    const pins = [];
	    const properties = [];
	    const texts = [];
	    const rawLines = content.split(/\r?\n/);
	    for (let i = 0; i < rawLines.length; i++) {
	        const line = rawLines[i].trim();
	        if (line.length === 0 || line === '+') {
	            continue;
	        }
	        const recordType = line[0];
	        const rest = line.slice(1).trim();
	        switch (recordType) {
	            case 'L':
	            case 'M': {
	                const parsed = parseLineRecord(rest);
	                if (parsed !== null) {
	                    lines.push(parsed);
	                }
	                break;
	            }
	            case 'A': {
	                const parsed = parseArcRecord(rest);
	                if (parsed !== null) {
	                    arcs.push(parsed);
	                }
	                break;
	            }
	            case 'C': {
	                const parsed = parsePinRecord(rest);
	                if (parsed !== null) {
	                    pins.push(parsed);
	                }
	                break;
	            }
	            case 'P':
	            case 'X': {
	                const parsed = parsePropertyRecord(recordType, rest);
	                if (parsed !== null) {
	                    properties.push(parsed);
	                }
	                break;
	            }
	            case 'T': {
	                const parsed = parseTextRecord(rest);
	                if (parsed !== null) {
	                    const next = rawLines[i + 1]?.trim();
	                    if (next !== undefined && next.length > 0 && !isRecordHeader(next)) {
	                        parsed.value = next;
	                        i++;
	                    }
	                    texts.push(parsed);
	                }
	                break;
	            }
	        }
	    }
	    return { lines, arcs, pins, properties, texts };
	}
	const RECORD_HEADERS = new Set(['L', 'M', 'A', 'C', 'P', 'X', 'T']);
	function isRecordHeader(line) {
	    return line.length > 0 && RECORD_HEADERS.has(line[0]) && (line.length === 1 || line[1] === ' ' || line[1] === '\t');
	}
	function parseArcRecord(body) {
	    const t = tokenize(body);
	    if (t.length < 5) {
	        return null;
	    }
	    const cx = parseFloat(t[0]);
	    const cy = parseFloat(t[1]);
	    const radius = parseFloat(t[2]);
	    const startDeg = parseFloat(t[3]);
	    const endDeg = parseFloat(t[4]);
	    if ([cx, cy, radius, startDeg, endDeg].some((v) => Number.isNaN(v))) {
	        return null;
	    }
	    return { type: 'A', cx, cy, radius, startDeg, endDeg };
	}
	function tokenize(body) {
	    const tokens = [];
	    let i = 0;
	    while (i < body.length) {
	        const ch = body[i];
	        if (ch === ' ' || ch === '\t') {
	            i++;
	            continue;
	        }
	        if (ch === '"') {
	            const end = body.indexOf('"', i + 1);
	            if (end < 0) {
	                tokens.push(body.slice(i + 1));
	                i = body.length;
	                break;
	            }
	            tokens.push(body.slice(i + 1, end));
	            i = end + 1;
	            continue;
	        }
	        let end = i;
	        while (end < body.length && body[end] !== ' ' && body[end] !== '\t') {
	            end++;
	        }
	        tokens.push(body.slice(i, end));
	        i = end;
	    }
	    return tokens;
	}
	function parseLineRecord(body) {
	    const t = tokenize(body);
	    if (t.length < 4) {
	        return null;
	    }
	    const x1 = parseFloat(t[0]);
	    const y1 = parseFloat(t[1]);
	    const x2 = parseFloat(t[2]);
	    const y2 = parseFloat(t[3]);
	    if (Number.isNaN(x1) || Number.isNaN(y1) || Number.isNaN(x2) || Number.isNaN(y2)) {
	        return null;
	    }
	    return { type: 'L', x1, y1, x2, y2 };
	}
	function parsePinRecord(body) {
	    const t = tokenize(body);
	    if (t.length < 5) {
	        return null;
	    }
	    const hotX = parseFloat(t[0]);
	    const hotY = parseFloat(t[1]);
	    const pinNumber = t[2];
	    const bodyX = parseFloat(t[3]);
	    const bodyY = parseFloat(t[4]);
	    if (Number.isNaN(bodyX) || Number.isNaN(bodyY) || Number.isNaN(hotX) || Number.isNaN(hotY)) {
	        return null;
	    }
	    const orientation = t.length > 5 && /^[LRUD]$/.test(t[t.length - 1]) ? t[t.length - 1] : '';
	    return { type: 'C', bodyX, bodyY, hotX, hotY, pinNumber, orientation };
	}
	function parsePropertyRecord(recordType, body) {
	    const t = tokenize(body);
	    if (t.length < 4) {
	        return null;
	    }
	    const x = parseFloat(t[2]);
	    const y = parseFloat(t[3]);
	    if (Number.isNaN(x) || Number.isNaN(y)) {
	        return null;
	    }
	    let just = null;
	    if (t.length > 9) {
	        const j = parseInt(t[9], 10);
	        if (!Number.isNaN(j) && j >= 0 && j <= 8) {
	            just = j;
	        }
	    }
	    return { type: recordType, propName: t[0], propValue: t[1], x, y, just };
	}
	function parseTextRecord(body) {
	    const t = tokenize(body);
	    if (t.length < 2) {
	        return null;
	    }
	    const x = parseFloat(t[0]);
	    const y = parseFloat(t[1]);
	    if (Number.isNaN(x) || Number.isNaN(y)) {
	        return null;
	    }
	    const value = t.find((tok, idx) => idx >= 6 && tok.length > 0);
	    return { type: 'T', x, y, value };
	}
	
	return cssParser;
}

var AllegroResolved = {};

var hasRequiredAllegroResolved;

function requireAllegroResolved () {
	if (hasRequiredAllegroResolved) return AllegroResolved;
	hasRequiredAllegroResolved = 1;
	Object.defineProperty(AllegroResolved, "__esModule", { value: true });
	AllegroResolved.allegroSymbolRefForCell = allegroSymbolRefForCell;
	function allegroSymbolRefForCell(cellName, view, sectionNumber = 1) {
	    return `${cellName}__${view}__sec${sectionNumber}__sym`;
	}
	
	return AllegroResolved;
}

var hasRequiredAllegroTransformComponentGenerator;

function requireAllegroTransformComponentGenerator () {
	if (hasRequiredAllegroTransformComponentGenerator) return AllegroTransformComponentGenerator;
	hasRequiredAllegroTransformComponentGenerator = 1;
	Object.defineProperty(AllegroTransformComponentGenerator, "__esModule", { value: true });
	AllegroTransformComponentGenerator.AllegroTransformComponentGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformProject_1 = requireTransformProject();
	const TransformComponent_1 = requireTransformComponent();
	const TransformComponentSymbol_1 = requireTransformComponentSymbol();
	const cssParser_1 = requireCssParser();
	const allegroLayers_1 = requireAllegroLayers();
	const AllegroResolved_1 = requireAllegroResolved();
	const { SchLayerType } = sigma_engine_shared_1.DesignModels;
	const PLACEHOLDER_PIN_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.Pin].id;
	const PIN_NAME_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.PinName].id;
	const PIN_NUMBER_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.PinNumber].id;
	const NAME_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.NameLabel].id;
	const VALUE_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.ValueLabel].id;
	const PLACEHOLDER_SYMBOL_BODY_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.Symbol].id;
	const PLACEHOLDER_SYMBOL_HALF_WIDTH = 2.5;
	const PLACEHOLDER_PIN_LENGTH = 1;
	const PLACEHOLDER_PIN_SPACING = 2.5;
	class AllegroTransformComponentGenerator$1 {
	    constructor() {
	        this.runsFor = [TransformProject_1.TransformProject];
	    }
	    *generate(project, rootContext) {
	        const bodyStroke = rootContext.resolved.symbolBodyStroke;
	        const instPropTextStyle = rootContext.resolved.instPropTextStyle;
	        const pinPropTextStyle = rootContext.resolved.pinPropTextStyle;
	        for (const resolved of rootContext.resolved.components) {
	            yield* this.emitComponent(project, rootContext, resolved, bodyStroke, instPropTextStyle, pinPropTextStyle);
	        }
	    }
	    *emitComponent(project, rootContext, resolved, bodyStroke, instPropTextStyle, pinPropTextStyle) {
	        const numPins = resolved.isVirtual ?
	            resolved.views.reduce((sum, v) => sum + v.terms.length, 0)
	            : (resolved.views[0]?.terms.length ?? 0);
	        const component = new TransformComponent_1.TransformComponent(project, {
	            baseComponent: {
	                name: resolved.cellName,
	                numPins,
	                numPads: 0,
	                footprints: [],
	                symbols: [],
	                signals: [],
	                ...(resolved.isVirtual ? { isVirtual: true } : {}),
	            },
	        });
	        yield component;
	        if (!rootContext.componentsByCellName.has(resolved.cellName)) {
	            rootContext.componentsByCellName.set(resolved.cellName, component);
	        }
	        const hasMultipleSections = new Set(resolved.views.map((v) => v.sectionNumber)).size > 1;
	        for (const view of resolved.views) {
	            const viewOpts = this.buildSymbolOptionsForView(resolved, view, hasMultipleSections);
	            yield this.buildSymbol(component, resolved, view, viewOpts, bodyStroke, instPropTextStyle, pinPropTextStyle);
	        }
	    }
	    buildSymbolOptionsForView(resolved, view, hasMultipleSections) {
	        if (resolved.isVirtual) {
	            return { pinNumberSuffix: `@${view.view}`, omitVisiblePinNumber: true };
	        }
	        if (hasMultipleSections) {
	            return { signalIdSuffix: `@s${view.sectionNumber}` };
	        }
	        return {};
	    }
	    buildSymbol(parent, resolved, view, options, bodyStroke, instPropTextStyle, pinPropTextStyle) {
	        const pinNumberFor = (logicalName) => {
	            const fromView = view.pinNumberByLogical.get(logicalName);
	            if (fromView !== undefined && fromView.length > 0) {
	                return fromView;
	            }
	            return logicalName;
	        };
	        const pins = view.css !== null ?
	            this.buildPinsFromCss(view, pinNumberFor, options, pinPropTextStyle)
	            : view.terms.map((term, idx) => this.buildPlaceholderPin(term.name, pinNumberFor(term.name), idx, view.terms.length, options, pinPropTextStyle));
	        const bodyDrawings = view.css !== null ? this.buildBodyDrawingsFromCss(view.css, bodyStroke) : [];
	        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(parent, {
	            ref: (0, AllegroResolved_1.allegroSymbolRefForCell)(resolved.cellName, view.view, view.sectionNumber),
	            name: resolved.cellName,
	            pins,
	            drawings: [...bodyDrawings, ...this.buildLabelPlaceholders(instPropTextStyle)],
	        });
	        const sigSuffix = options.signalIdSuffix ?? '';
	        for (const pin of pins) {
	            const id = sigSuffix.length > 0 ? `${pin.name}${sigSuffix}` : pin.name;
	            symbol.setPinSignal(pin, { id, name: pin.name });
	        }
	        return symbol;
	    }
	    buildLabelPlaceholders(textStyle) {
	        const labelOffset = PLACEHOLDER_SYMBOL_HALF_WIDTH + textStyle.size * 2;
	        const colorOverride = textStyle.color !== null ? { colorOverride: textStyle.color } : {};
	        const attr = [];
	        if (textStyle.bold) {
	            attr.push('bold');
	        }
	        if (textStyle.italic) {
	            attr.push('italic');
	        }
	        const attrField = attr.length > 0 ? { attr } : {};
	        return [
	            {
	                layers: [NAME_LABEL_LAYER],
	                drawing: {
	                    type: 'text',
	                    ref: 'Part Reference',
	                    ref_designator: true,
	                    value: '?',
	                    pos: [0, -labelOffset],
	                    size: textStyle.size,
	                    justify: [-1, -1],
	                    thickness: 1,
	                    ...colorOverride,
	                    ...attrField,
	                },
	            },
	            {
	                layers: [VALUE_LABEL_LAYER],
	                drawing: {
	                    type: 'text',
	                    ref: 'Value',
	                    ref_designator: true,
	                    value: '?',
	                    pos: [0, labelOffset],
	                    size: textStyle.size,
	                    justify: [-1, -1],
	                    thickness: 1,
	                    ...colorOverride,
	                    ...attrField,
	                },
	            },
	        ];
	    }
	    buildPinsFromCss(view, pinNumberFor, options, pinTextStyle) {
	        const suffix = options.pinNumberSuffix ?? '';
	        const omitNumberDrawing = options.omitVisiblePinNumber ?? false;
	        return view.terms.map((term, idx) => {
	            const cssPin = view.pinByLower.get(term.name.toLowerCase());
	            const physicalNumber = pinNumberFor(term.name);
	            if (cssPin === undefined) {
	                return this.buildPlaceholderPin(term.name, physicalNumber, idx, view.terms.length, options, pinTextStyle);
	            }
	            const bodyEnd = this.cssToMm(cssPin.bodyX, cssPin.bodyY);
	            const hotEnd = this.cssToMm(cssPin.hotX, cssPin.hotY);
	            const refWithSuffix = suffix.length > 0 ? `${physicalNumber}${suffix}` : physicalNumber;
	            return {
	                type: 'pin',
	                ref: refWithSuffix,
	                name: term.name,
	                number: refWithSuffix,
	                start: bodyEnd,
	                end: hotEnd,
	                layer: PLACEHOLDER_PIN_LAYER,
	                drawings: this.buildPinTextDrawings(term.name, physicalNumber, bodyEnd, hotEnd, omitNumberDrawing, pinTextStyle),
	            };
	        });
	    }
	    buildBodyDrawingsFromCss(css, bodyStroke) {
	        const colorOverride = bodyStroke.color !== null ? { colorOverride: bodyStroke.color } : {};
	        const segments = css.lines.map((line) => ({
	            layers: [PLACEHOLDER_SYMBOL_BODY_LAYER],
	            drawing: {
	                type: 'segment',
	                start: this.cssToMm(line.x1, line.y1),
	                end: this.cssToMm(line.x2, line.y2),
	                width: bodyStroke.width,
	                ...colorOverride,
	            },
	        }));
	        const arcs = css.arcs.map((arc) => {
	            const center = this.cssToMm(arc.cx, arc.cy);
	            const radius = arc.radius * cssParser_1.CSS_UNIT_TO_MM;
	            if (Math.abs(arc.endDeg - arc.startDeg) >= 360 - 1e-3) {
	                return {
	                    layers: [PLACEHOLDER_SYMBOL_BODY_LAYER],
	                    drawing: { type: 'circle', center, radius, filled: false, width: bodyStroke.width, ...colorOverride },
	                };
	            }
	            return {
	                layers: [PLACEHOLDER_SYMBOL_BODY_LAYER],
	                drawing: {
	                    type: 'arc',
	                    center,
	                    radius,
	                    startAngle: -arc.endDeg,
	                    endAngle: -arc.startDeg,
	                    lineWidth: bodyStroke.width,
	                    ...colorOverride,
	                },
	            };
	        });
	        return [...segments, ...arcs];
	    }
	    cssToMm(x, y) {
	        return [x * cssParser_1.CSS_UNIT_TO_MM, -y * cssParser_1.CSS_UNIT_TO_MM];
	    }
	    buildPlaceholderPin(termName, physicalPinNumber, pinIndex, totalPins, options, pinTextStyle) {
	        const suffix = options.pinNumberSuffix ?? '';
	        const omitNumberDrawing = options.omitVisiblePinNumber ?? false;
	        const sideCount = Math.ceil(totalPins / 2);
	        const onLeftSide = pinIndex % 2 === 0;
	        const sideIndex = Math.floor(pinIndex / 2);
	        const verticalSpan = (sideCount - 1) * PLACEHOLDER_PIN_SPACING;
	        const y = -verticalSpan / 2 + sideIndex * PLACEHOLDER_PIN_SPACING;
	        const bodyEdgeX = onLeftSide ? -PLACEHOLDER_SYMBOL_HALF_WIDTH : PLACEHOLDER_SYMBOL_HALF_WIDTH;
	        const outsideX = onLeftSide ?
	            -(PLACEHOLDER_SYMBOL_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH)
	            : PLACEHOLDER_SYMBOL_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH;
	        const bodyEnd = [bodyEdgeX, y];
	        const hotEnd = [outsideX, y];
	        const refWithSuffix = suffix.length > 0 ? `${physicalPinNumber}${suffix}` : physicalPinNumber;
	        return {
	            type: 'pin',
	            ref: refWithSuffix,
	            name: termName,
	            number: refWithSuffix,
	            start: bodyEnd,
	            end: hotEnd,
	            layer: PLACEHOLDER_PIN_LAYER,
	            drawings: this.buildPinTextDrawings(termName, physicalPinNumber, bodyEnd, hotEnd, omitNumberDrawing, pinTextStyle),
	        };
	    }
	    buildPinTextDrawings(pinName, pinNumber, bodyEnd, hotEnd, omitVisiblePinNumber, textStyle) {
	        const [bodyX, bodyY] = bodyEnd;
	        const [hotX, hotY] = hotEnd;
	        let angle;
	        let nameJustify;
	        const dx = hotX - bodyX;
	        const dy = hotY - bodyY;
	        if (Math.abs(dx) >= Math.abs(dy)) {
	            angle = 0;
	            nameJustify = dx > 0 ? [1, 0] : [-1, 0];
	        }
	        else {
	            angle = 90;
	            nameJustify = dy > 0 ? [-1, 0] : [1, 0];
	        }
	        const size = textStyle.size;
	        const estHalfWidth = (pinName.length * size * 0.6) / 2;
	        const perpOffset = size * 1.2;
	        const nameOffset = angle === 0 ? [estHalfWidth * -nameJustify[0], 0] : [0, estHalfWidth * nameJustify[0]];
	        const numberOffset = angle === 0 ? [0, -perpOffset] : [-perpOffset, 0];
	        const midX = (bodyX + hotX) / 2;
	        const midY = (bodyY + hotY) / 2;
	        const colorOverride = textStyle.color !== null ? { colorOverride: textStyle.color } : {};
	        const attr = [];
	        if (textStyle.bold) {
	            attr.push('bold');
	        }
	        if (textStyle.italic) {
	            attr.push('italic');
	        }
	        const attrField = attr.length > 0 ? { attr } : {};
	        const drawings = [
	            {
	                layers: [PIN_NAME_LAYER],
	                drawing: {
	                    type: 'text',
	                    value: pinName,
	                    pos: [bodyX + nameOffset[0], bodyY + nameOffset[1]],
	                    angle,
	                    justify: [0, 0],
	                    size,
	                    thickness: 1,
	                    rejustifyOnRotate: true,
	                    ...colorOverride,
	                    ...attrField,
	                },
	            },
	        ];
	        if (!omitVisiblePinNumber) {
	            drawings.push({
	                layers: [PIN_NUMBER_LAYER],
	                drawing: {
	                    type: 'text',
	                    value: pinNumber,
	                    pos: [midX + numberOffset[0], midY + numberOffset[1]],
	                    angle,
	                    justify: [0, 0],
	                    size,
	                    thickness: 1,
	                    rejustifyOnRotate: true,
	                    ...colorOverride,
	                    ...attrField,
	                },
	            });
	        }
	        return drawings;
	    }
	}
	AllegroTransformComponentGenerator.AllegroTransformComponentGenerator = AllegroTransformComponentGenerator$1;
	
	return AllegroTransformComponentGenerator;
}

var AllegroTransformModuleGenerator = {};

var decodePropBlock = {};

var decodedTypes = {};

var hasRequiredDecodedTypes;

function requireDecodedTypes () {
	if (hasRequiredDecodedTypes) return decodedTypes;
	hasRequiredDecodedTypes = 1;
	Object.defineProperty(decodedTypes, "__esModule", { value: true });
	decodedTypes.DecodeWarningType = void 0;
	var DecodeWarningType;
	(function (DecodeWarningType) {
	    DecodeWarningType["UnknownTypeCode"] = "allegro-decode-unknown-typecode";
	    DecodeWarningType["UnrecognizedShape"] = "allegro-decode-unrecognized-shape";
	    DecodeWarningType["CrossRefMiss"] = "allegro-decode-cross-ref-miss";
	    DecodeWarningType["InvariantViolation"] = "allegro-decode-invariant-violation";
	})(DecodeWarningType || (decodedTypes.DecodeWarningType = DecodeWarningType = {}));
	
	return decodedTypes;
}

var entityList = {};

var typeCodes = {};

var hasRequiredTypeCodes;

function requireTypeCodes () {
	if (hasRequiredTypeCodes) return typeCodes;
	hasRequiredTypeCodes = 1;
	Object.defineProperty(typeCodes, "__esModule", { value: true });
	typeCodes.RootTypeCode = typeCodes.TypeCode = void 0;
	typeCodes.asTypeCode = asTypeCode;
	var TypeCode;
	(function (TypeCode) {
	    TypeCode["NetStub"] = "5";
	    TypeCode["PageBorder"] = "6";
	    TypeCode["PowerSymbol"] = "7";
	    TypeCode["WireBundle"] = "8";
	    TypeCode["BusSegment"] = "10";
	    TypeCode["WireSegment"] = "11";
	    TypeCode["ComponentInstance"] = "12";
	    TypeCode["Terminal"] = "13";
	    TypeCode["PageMetadata"] = "14";
	    TypeCode["GraphicsLineArrow"] = "25";
	    TypeCode["GraphicsCallout"] = "28";
	    TypeCode["GraphicsHtml"] = "29";
	    TypeCode["VisualLabel"] = "31";
	    TypeCode["GraphicsImage"] = "33";
	    TypeCode["PropList"] = "37";
	    TypeCode["PropBlock"] = "40";
	    TypeCode["PageInstance"] = "55";
	})(TypeCode || (typeCodes.TypeCode = TypeCode = {}));
	var RootTypeCode;
	(function (RootTypeCode) {
	    RootTypeCode[RootTypeCode["Master"] = 3] = "Master";
	    RootTypeCode[RootTypeCode["Page"] = 4] = "Page";
	})(RootTypeCode || (typeCodes.RootTypeCode = RootTypeCode = {}));
	const TYPE_CODE_VALUES = new Set(Object.values(TypeCode));
	function asTypeCode(s) {
	    return TYPE_CODE_VALUES.has(s) ? s : null;
	}
	
	return typeCodes;
}

var hasRequiredEntityList;

function requireEntityList () {
	if (hasRequiredEntityList) return entityList;
	hasRequiredEntityList = 1;
	Object.defineProperty(entityList, "__esModule", { value: true });
	entityList.detectEntityList = detectEntityList;
	entityList.entityListItems = entityListItems;
	entityList.decodeEntityList = decodeEntityList;
	entityList.isPlaceholderContainer = isPlaceholderContainer;
	const decoded_types_1 = requireDecodedTypes();
	const typeCodes_1 = requireTypeCodes();
	const sigma_engine_shared_1 = requireDist$1();
	function detectEntityList(children) {
	    if (children.length < 2) {
	        return null;
	    }
	    const first = children[0];
	    const second = children[1];
	    if (first.type !== 'atom' || second.type !== 'atom') {
	        return null;
	    }
	    const declaredCount = parseInt(first.content, 10);
	    if (!Number.isFinite(declaredCount)) {
	        return null;
	    }
	    const typeCode = second.content.trim();
	    if (!/^\d+$/.test(typeCode)) {
	        return null;
	    }
	    return { typeCode, declaredCount };
	}
	function entityListItems(node) {
	    const out = [];
	    for (let i = 2; i < node.children.length; i++) {
	        const child = node.children[i];
	        if (child.type === 'atom') {
	            continue;
	        }
	        out.push(child);
	    }
	    return out;
	}
	function decodeEntityList(node, expectedTypeCode, path, warnings, decodeItem) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for entity-list, found ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return null;
	    }
	    const detected = detectEntityList(node.children);
	    if (detected === null) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `container does not open with (count, typeCode) atoms`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return null;
	    }
	    if (expectedTypeCode !== null && detected.typeCode !== expectedTypeCode) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
	            message: `expected typeCode=${expectedTypeCode}, found ${detected.typeCode}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return null;
	    }
	    const itemNodes = entityListItems(node);
	    if (itemNodes.length !== detected.declaredCount) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `entity-list declared count=${detected.declaredCount} but found ${itemNodes.length} items`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path,
	        });
	    }
	    const declaredTypeCode = expectedTypeCode !== null ? expectedTypeCode : ((0, typeCodes_1.asTypeCode)(detected.typeCode) ?? detected.typeCode);
	    const items = itemNodes.map((item, index) => decodeItem(item, index, `${path}/items[${index}]`));
	    return {
	        kind: 'entity-list',
	        declaredTypeCode,
	        declaredCount: detected.declaredCount,
	        items,
	        raw: node,
	    };
	}
	function isPlaceholderContainer(node) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        return false;
	    }
	    if (node.children.length !== 1) {
	        return false;
	    }
	    const only = node.children[0];
	    return only.type === 'atom' && only.content.trim() === '0';
	}
	
	return entityList;
}

var hasRequiredDecodePropBlock;

function requireDecodePropBlock () {
	if (hasRequiredDecodePropBlock) return decodePropBlock;
	hasRequiredDecodePropBlock = 1;
	Object.defineProperty(decodePropBlock, "__esModule", { value: true });
	decodePropBlock.decodePropBlock = decodePropBlock$1;
	decodePropBlock.findProp = findProp;
	decodePropBlock.findPropValue = findPropValue;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const entity_list_1 = requireEntityList();
	function decodePropBlock$1(node, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        return null;
	    }
	    const detected = (0, entity_list_1.detectEntityList)(node.children);
	    if (detected === null) {
	        return null;
	    }
	    if (detected.typeCode !== '40' && detected.typeCode !== '37') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
	            message: `expected prop block typeCode=40 or 37, found ${detected.typeCode}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return null;
	    }
	    const props = [];
	    const children = node.children;
	    for (let i = 2; i < children.length; i++) {
	        const candidate = children[i];
	        if (candidate.type !== 'name') {
	            continue;
	        }
	        let propTypeCode = detected.typeCode;
	        for (let j = i - 1; j >= 2; j--) {
	            const prev = children[j];
	            if (prev.type !== 'atom') {
	                break;
	            }
	            const c = prev.content.trim();
	            if (c === '40' || c === '37') {
	                propTypeCode = c;
	                break;
	            }
	        }
	        let cursor = i + 1;
	        while (cursor < children.length && children[cursor].type === 'atom') {
	            cursor++;
	        }
	        const valueNode = children[cursor];
	        if (valueNode === undefined || valueNode.type !== 'value') {
	            warnings.push({
	                type: decoded_types_1.DecodeWarningType.InvariantViolation,
	                message: `expected value node after name "${candidate.content}"`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                path: `${path}/props[${props.length}]`,
	            });
	            continue;
	        }
	        props.push({
	            kind: 'prop',
	            propTypeCode,
	            name: candidate.content.trim(),
	            value: valueNode.content.trim(),
	            raw: candidate,
	        });
	        i = cursor;
	    }
	    if (props.length !== detected.declaredCount) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `prop-block declared count=${detected.declaredCount} but decoded ${props.length} props`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path,
	        });
	    }
	    return {
	        kind: 'prop-block',
	        declaredCount: detected.declaredCount,
	        props,
	        raw: node,
	    };
	}
	function findProp(block, name) {
	    if (block === null) {
	        return null;
	    }
	    for (const p of block.props) {
	        if (p.name === name) {
	            return p;
	        }
	    }
	    return null;
	}
	function findPropValue(block, name) {
	    const p = findProp(block, name);
	    return p === null ? null : p.value;
	}
	
	return decodePropBlock;
}

var AllegroRecordHelpers = {};

var helpers = {};

var hasRequiredHelpers;

function requireHelpers () {
	if (hasRequiredHelpers) return helpers;
	hasRequiredHelpers = 1;
	Object.defineProperty(helpers, "__esModule", { value: true });
	helpers.decodeCadenceName = decodeCadenceName;
	helpers.atomContent = atomContent;
	helpers.childrenOf = childrenOf;
	helpers.findPrefixed = findPrefixed;
	helpers.findPrefixedDeep = findPrefixedDeep;
	helpers.findCoordsContainer = findCoordsContainer;
	helpers.extractBBox = extractBBox;
	helpers.getNamedValue = getNamedValue;
	helpers.getNamedValueDeep = getNamedValueDeep;
	function decodeCadenceName(s) {
	    return s.replace(/#([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
	}
	function atomContent(node) {
	    return node.type === 'atom' ? node.content : null;
	}
	function childrenOf(node) {
	    return node.type === 'container' || node.type === 'econtainer' ? node.children : [];
	}
	function findPrefixed(nodes, prefix) {
	    const full = `${prefix}:`;
	    for (const node of nodes) {
	        const content = atomContent(node);
	        if (content !== null && content.startsWith(full)) {
	            return content.slice(full.length);
	        }
	    }
	    return null;
	}
	function findPrefixedDeep(node, prefix) {
	    const full = `${prefix}:`;
	    const direct = atomContent(node);
	    if (direct !== null && direct.startsWith(full)) {
	        return direct.slice(full.length);
	    }
	    for (const child of childrenOf(node)) {
	        const found = findPrefixedDeep(child, prefix);
	        if (found !== null) {
	            return found;
	        }
	    }
	    return null;
	}
	function findCoordsContainer(nodes) {
	    for (const node of nodes) {
	        if (node.type !== 'container' && node.type !== 'econtainer') {
	            continue;
	        }
	        const children = node.children;
	        if (children.length >= 4 && children.every((c) => c.type === 'atom')) {
	            return node;
	        }
	        const deep = findCoordsContainer(children);
	        if (deep !== null) {
	            return deep;
	        }
	    }
	    return null;
	}
	function extractBBox(node) {
	    const container = findCoordsContainer(childrenOf(node));
	    if (container === null) {
	        return null;
	    }
	    const values = childrenOf(container).map((c) => parseInt(atomContent(c) ?? '0', 10));
	    if (values.length < 4) {
	        return null;
	    }
	    return { x1: values[0], y1: values[1], x2: values[2], y2: values[3] };
	}
	function getNamedValue(node, name) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        return null;
	    }
	    const children = node.children;
	    for (let i = 0; i < children.length; i++) {
	        const c = children[i];
	        if (c.type === 'name' && c.content === name) {
	            for (let j = i + 1; j < Math.min(i + 4, children.length); j++) {
	                const candidate = children[j];
	                if (candidate.type === 'value') {
	                    return candidate.content;
	                }
	            }
	        }
	    }
	    return null;
	}
	function getNamedValueDeep(node, name) {
	    const direct = getNamedValue(node, name);
	    if (direct !== null) {
	        return direct;
	    }
	    for (const child of childrenOf(node)) {
	        const found = getNamedValueDeep(child, name);
	        if (found !== null) {
	            return found;
	        }
	    }
	    return null;
	}
	
	return helpers;
}

var decodeTc31VisualLabel = {};

var hasRequiredDecodeTc31VisualLabel;

function requireDecodeTc31VisualLabel () {
	if (hasRequiredDecodeTc31VisualLabel) return decodeTc31VisualLabel;
	hasRequiredDecodeTc31VisualLabel = 1;
	Object.defineProperty(decodeTc31VisualLabel, "__esModule", { value: true });
	decodeTc31VisualLabel.decodeVisualLabel = decodeVisualLabel;
	decodeTc31VisualLabel.readCoordsContainer = readCoordsContainer;
	decodeTc31VisualLabel.readCadencePosition = readCadencePosition;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const decode_prop_block_1 = requireDecodePropBlock();
	function decodeVisualLabel(node, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for visual label, got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const c = node.children;
	    if (c.length !== 13) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `visual label has ${c.length} children, expected 13`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const intAtom = (n) => n !== undefined && n.type === 'atom' && /^\d+$/.test(n.content.trim()) ? n.content.trim() : null;
	    const styleRowIndex = intAtom(c[3]);
	    const secondaryStyleRowIndex = intAtom(c[11]);
	    const propertyName = c[4].type === 'atom' ? c[4].content.trim() : null;
	    const bbox = readCoordsContainer(c[6]);
	    const position = readCadencePosition(c[8]);
	    const displayFlag = c[10].type === 'atom' ? c[10].content.trim() : null;
	    const props = (0, decode_prop_block_1.decodePropBlock)(c[12], `${path}/props`, warnings);
	    return {
	        kind: 'visual-label',
	        styleRowIndex,
	        secondaryStyleRowIndex,
	        propertyName,
	        bbox,
	        position,
	        displayFlag,
	        props,
	        raw: node,
	    };
	}
	function readCoordsContainer(node) {
	    const ints = readFourAtomInts(node);
	    if (ints === null) {
	        return null;
	    }
	    return { x1: ints[0], y1: ints[1], x2: ints[2], y2: ints[3] };
	}
	function readCadencePosition(node) {
	    const ints = readFourAtomInts(node);
	    if (ints === null) {
	        return null;
	    }
	    return { x: ints[3], y: ints[1] };
	}
	function readFourAtomInts(node) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        return null;
	    }
	    if (node.children.length < 4) {
	        return null;
	    }
	    const ints = [];
	    for (let i = 0; i < 4; i++) {
	        const child = node.children[i];
	        if (child.type !== 'atom') {
	            return null;
	        }
	        const n = parseInt(child.content, 10);
	        if (!Number.isFinite(n)) {
	            return null;
	        }
	        ints.push(n);
	    }
	    return [ints[0], ints[1], ints[2], ints[3]];
	}
	
	return decodeTc31VisualLabel;
}

var hasRequiredAllegroRecordHelpers;

function requireAllegroRecordHelpers () {
	if (hasRequiredAllegroRecordHelpers) return AllegroRecordHelpers;
	hasRequiredAllegroRecordHelpers = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.CADENCE_SCH_UNIT_TO_MM = void 0;
		exports.modelNameOf = modelNameOf;
		exports.viewOf = viewOf;
		exports.isOffpageConnectorModelRef = isOffpageConnectorModelRef;
		exports.pinNumberFromTerminal = pinNumberFromTerminal;
		exports.bboxToMm = bboxToMm;
		exports.entityItems = entityItems;
		exports.iterateComponentInstances = iterateComponentInstances;
		exports.iteratePowerSymbols = iteratePowerSymbols;
		exports.iterateNetStubs = iterateNetStubs;
		exports.terminalHotpointMm = terminalHotpointMm;
		exports.iterateTerminals = iterateTerminals;
		exports.iterateVisualLabels = iterateVisualLabels;
		exports.iterateWireBundles = iterateWireBundles;
		exports.iterateWireSegments = iterateWireSegments;
		exports.parseTransformMatrix = parseTransformMatrix;
		exports.parseRotationDeg = parseRotationDeg;
		const helpers_1 = requireHelpers();
		const decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
		exports.CADENCE_SCH_UNIT_TO_MM = 0.000_254;
		function modelNameOf(modelRef) {
		    if (modelRef === null || modelRef.length === 0) {
		        return '';
		    }
		    const idx = modelRef.indexOf('##');
		    const raw = idx < 0 ? modelRef : modelRef.slice(0, idx);
		    return (0, helpers_1.decodeCadenceName)(raw);
		}
		function viewOf(modelRef) {
		    if (modelRef === null) {
		        return '';
		    }
		    const sepIdx = modelRef.indexOf('##');
		    if (sepIdx < 0) {
		        return '';
		    }
		    const after = modelRef.slice(sepIdx + 2);
		    const colonIdx = after.indexOf(':');
		    return colonIdx < 0 ? after : after.slice(0, colonIdx);
		}
		function isOffpageConnectorModelRef(modelRef) {
		    return modelRef !== null && /^offpage##sym_[123](:|$)/.test(modelRef);
		}
		function pinNumberFromTerminal(terminal) {
		    if (terminal.subReference === null) {
		        return null;
		    }
		    const stripped = terminal.subReference.startsWith('19:') ? terminal.subReference.slice(3) : terminal.subReference;
		    const idx = stripped.indexOf('!~');
		    return idx < 0 ? stripped : stripped.slice(0, idx);
		}
		function bboxToMm(bbox) {
		    if (bbox === null) {
		        return null;
		    }
		    return {
		        x1: bbox.x1 * exports.CADENCE_SCH_UNIT_TO_MM,
		        y1: bbox.y1 * exports.CADENCE_SCH_UNIT_TO_MM,
		        x2: bbox.x2 * exports.CADENCE_SCH_UNIT_TO_MM,
		        y2: bbox.y2 * exports.CADENCE_SCH_UNIT_TO_MM,
		    };
		}
		function* entityItems(slot, expectedKind) {
		    if (slot === null || !('items' in slot)) {
		        return;
		    }
		    for (const item of slot.items) {
		        if ('kind' in item && item.kind === expectedKind) {
		            yield item;
		        }
		    }
		}
		function iterateComponentInstances(body) {
		    return entityItems(body.slots.componentInstances, 'component-instance');
		}
		function iteratePowerSymbols(body) {
		    return entityItems(body.slots.powerSymbols, 'power-symbol');
		}
		function iterateNetStubs(body) {
		    return entityItems(body.slots.netStubs, 'net-stub');
		}
		function terminalHotpointMm(terminal) {
		    const c = terminal.positionContainer;
		    if (c === null) {
		        return null;
		    }
		    const pos = (0, decode_tc31_visual_label_1.readCadencePosition)(c);
		    if (pos === null || (pos.x === 0 && pos.y === 0)) {
		        return null;
		    }
		    return { x: pos.x * exports.CADENCE_SCH_UNIT_TO_MM, y: pos.y * exports.CADENCE_SCH_UNIT_TO_MM };
		}
		function iterateTerminals(instance) {
		    return entityItems(instance.terminals, 'terminal');
		}
		function iterateVisualLabels(instance) {
		    return entityItems(instance.visualLabels, 'visual-label');
		}
		function iterateWireBundles(stub) {
		    return entityItems(stub.wireBundles, 'wire-bundle');
		}
		function iterateWireSegments(bundle) {
		    return entityItems(bundle.segments, 'wire-segment');
		}
		function parseTransformMatrix(transform) {
		    if (transform === null || transform.length === 0) {
		        return null;
		    }
		    const parts = transform.split(',').map((s) => parseFloat(s.trim()));
		    if (parts.length < 9 || parts.some((n) => !Number.isFinite(n))) {
		        return null;
		    }
		    return parts.slice(0, 9);
		}
		function parseRotationDeg(rotation) {
		    if (rotation === null) {
		        return null;
		    }
		    const n = parseFloat(rotation);
		    return Number.isFinite(n) ? n : null;
		}
		
	} (AllegroRecordHelpers));
	return AllegroRecordHelpers;
}

var AllegroResolveDesign = {};

var hasRequiredAllegroResolveDesign;

function requireAllegroResolveDesign () {
	if (hasRequiredAllegroResolveDesign) return AllegroResolveDesign;
	hasRequiredAllegroResolveDesign = 1;
	Object.defineProperty(AllegroResolveDesign, "__esModule", { value: true });
	AllegroResolveDesign.resolveAllegroProject = resolveAllegroProject;
	AllegroResolveDesign.resolveExplicitStrokeStyle = resolveExplicitStrokeStyle;
	AllegroResolveDesign.resolveExplicitTextStyle = resolveExplicitTextStyle;
	AllegroResolveDesign.parseQRichText = parseQRichText;
	const sigma_engine_shared_1 = requireDist$1();
	const Transform_1 = requireTransform();
	const cssParser_1 = requireCssParser();
	const AllegroRecordHelpers_1 = requireAllegroRecordHelpers();
	const decode_prop_block_1 = requireDecodePropBlock();
	const decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
	const AllegroTransformModuleGenerator_1 = requireAllegroTransformModuleGenerator();
	const DEFAULT_WIRE_WIDTH_MM = 0.1;
	const WIRE_STYLE_ROW_INDEX = '1';
	const STYLE_LINE_WIDTH_TO_MM = 0.3;
	const BUS_WIDTH_MULTIPLIER = 2;
	const STYLE_FONT_SIZE_TO_MM = 25.4 / 72;
	const DEFAULT_TEXT_SIZE_MM = 1.27;
	const JUNCTION_QUANTIZE = 1_000;
	const GRID_SPACING = 10;
	const GRID_COLUMNS = 8;
	function resolveAllegroProject(input) {
	    const components = resolveComponents(input);
	    const componentByCellName = new Map();
	    for (const component of components) {
	        componentByCellName.set(component.cellName, component);
	    }
	    const designs = input.sheetPages.map((page, i) => resolveDesign(page, i, input, componentByCellName));
	    const symbolBodyStroke = designs[0]?.wireStroke ?? { width: DEFAULT_WIRE_WIDTH_MM, color: null };
	    const projectStyleName = input.styleAssignmentByKey.get(`${designs[0]?.pageNum ?? '1'}:${WIRE_STYLE_ROW_INDEX}`);
	    const projectStyle = projectStyleName !== undefined ? input.styles.design.get(projectStyleName) : undefined;
	    const instPropTextStyle = textStyleFromProjectStyle(projectStyle, projectStyle?.instPropFontSize ?? null, 'sheet-default');
	    const pinPropTextStyle = textStyleFromProjectStyle(projectStyle, projectStyle?.pinPropFontSize ?? null, 'sheet-default');
	    return {
	        projectName: input.projectName,
	        components,
	        componentByCellName,
	        designs,
	        symbolBodyStroke,
	        instPropTextStyle,
	        pinPropTextStyle,
	    };
	}
	function resolveComponents(input) {
	    const components = [];
	    const seenCellNames = new Set();
	    const xconDesign = input.parsedXcon?.designs[0];
	    if (xconDesign !== undefined) {
	        const byName = new Map();
	        for (const cell of xconDesign.cells.values()) {
	            const baseView = resolveComponentView(input, cell.library, cell.name, cell.view, cell.terms) ??
	                buildResolvedView(cell.library, cell.view, null, cell.terms, 1, new Map());
	            const bucket = byName.get(cell.name);
	            if (bucket === undefined) {
	                byName.set(cell.name, { primary: cell, baseViews: [baseView] });
	            }
	            else if (!bucket.baseViews.some((v) => v.view === baseView.view)) {
	                bucket.baseViews.push(baseView);
	            }
	            seenCellNames.add(cell.name);
	        }
	        for (const { primary, baseViews } of byName.values()) {
	            const chips = input.chipsPrtByCellName.get(primary.name.toLowerCase()) ?? null;
	            const fanBySection = baseViews.length === 1;
	            const sections = fanBySection ? sectionNumbersForCell(primary.name, input) : [1];
	            const views = [];
	            for (const base of baseViews) {
	                for (const section of sections) {
	                    const chipsCommaIndex = fanBySection ? section : viewSectionIndex(base.view);
	                    const fullMap = buildPinNumberByLogical(primary.name, fanBySection ? section : null, chipsCommaIndex, input, chips);
	                    const pinNumberByLogical = restrictToViewTerms(fullMap, base.terms);
	                    views.push({ ...base, sectionNumber: section, pinNumberByLogical });
	                }
	            }
	            components.push({
	                cellName: primary.name,
	                libraryName: primary.library.length > 0 ? primary.library : null,
	                xconCellId: primary.id.length > 0 ? primary.id : null,
	                views,
	                sectionCount: sections.length,
	                chips,
	                isVirtual: false,
	            });
	        }
	    }
	    const seen = seenCellNames;
	    const cssOnlyByCell = new Map();
	    for (const [key, css] of input.symbolCssByCell) {
	        const parts = key.split('__');
	        if (parts.length !== 3) {
	            continue;
	        }
	        const [library, name, view] = parts;
	        if (seen.has(name)) {
	            continue;
	        }
	        const synthesizedTerms = css.pins.map((p) => ({
	            id: '',
	            name: p.pinNumber.toLowerCase(),
	            direction: 'inout',
	        }));
	        const resolved = buildResolvedView(library, view, css, synthesizedTerms);
	        const bucket = cssOnlyByCell.get(name);
	        if (bucket === undefined) {
	            cssOnlyByCell.set(name, [resolved]);
	        }
	        else {
	            bucket.push(resolved);
	        }
	    }
	    for (const [name, views] of cssOnlyByCell) {
	        components.push({
	            cellName: name,
	            libraryName: views[0]?.library ?? null,
	            xconCellId: null,
	            views,
	            sectionCount: 1,
	            chips: input.chipsPrtByCellName.get(name.toLowerCase()) ?? null,
	            isVirtual: true,
	        });
	    }
	    return components;
	}
	function resolveComponentView(input, library, cellName, view, terms) {
	    const direct = input.symbolCssByCell.get(`${library}__${cellName}__${view}`);
	    if (direct !== undefined) {
	        return buildResolvedView(library, view, direct, terms);
	    }
	    const suffix = `__${cellName}__${view}`;
	    for (const [key, css] of input.symbolCssByCell) {
	        if (key.endsWith(suffix)) {
	            const fallbackLibrary = key.slice(0, key.length - suffix.length);
	            return buildResolvedView(fallbackLibrary, view, css, terms);
	        }
	    }
	    return null;
	}
	function buildResolvedView(library, view, css, terms, sectionNumber = 1, pinNumberByLogical = new Map()) {
	    return {
	        library,
	        view,
	        sectionNumber,
	        css,
	        pinByLower: css !== null ? buildCssPinLookup(css) : new Map(),
	        cssPinRefByOrdinal: css !== null ? css.pins.map((p) => p.pinNumber.toLowerCase()) : [],
	        terms,
	        pinNumberByLogical,
	    };
	}
	function restrictToViewTerms(full, terms) {
	    const allowed = new Set(terms.map((t) => t.name.toLowerCase()));
	    const out = new Map();
	    for (const [logical, value] of full) {
	        if (allowed.has(logical.toLowerCase())) {
	            out.set(logical, value);
	        }
	    }
	    return out;
	}
	function sectionNumbersForCell(cellName, input) {
	    const found = new Set();
	    for (const xconId of input.cellInstanceIds.get(cellName) ?? []) {
	        const sec = input.instanceSectionMap.get(xconId);
	        if (sec !== undefined && sec >= 1) {
	            found.add(sec);
	        }
	    }
	    if (found.size === 0) {
	        return [1];
	    }
	    return [...found].sort((a, b) => a - b);
	}
	function buildCssPinLookup(css) {
	    const out = new Map();
	    for (const pin of css.pins) {
	        if (pin.pinNumber.length > 0) {
	            out.set(pin.pinNumber.toLowerCase(), pin);
	        }
	    }
	    return out;
	}
	function buildPinNumberByLogical(cellName, dcfSectionFilter, chipsCommaIndex, input, chips) {
	    const out = new Map();
	    for (const xconId of input.cellInstanceIds.get(cellName) ?? []) {
	        if (dcfSectionFilter !== null) {
	            const instanceSection = input.instanceSectionMap.get(xconId) ?? 1;
	            if (instanceSection !== dcfSectionFilter) {
	                continue;
	            }
	        }
	        const pins = input.instancePinMap.get(xconId);
	        if (pins === undefined) {
	            continue;
	        }
	        for (const [logical, pn] of pins) {
	            const existing = out.get(logical);
	            if (existing === undefined) {
	                out.set(logical, pn);
	            }
	            else if (!existing.split(',').includes(pn)) {
	                out.set(logical, `${existing},${pn}`);
	            }
	        }
	    }
	    if (chips !== null) {
	        for (const [logical, value] of chips.pinNumberByName) {
	            if (out.has(logical)) {
	                continue;
	            }
	            if (value.includes(',')) {
	                const parts = value.split(',').map((p) => p.trim());
	                const selected = parts[chipsCommaIndex - 1] ?? parts[0];
	                if (selected !== undefined && selected !== '0' && selected.length > 0) {
	                    out.set(logical, selected);
	                }
	            }
	            else if (value.length > 0) {
	                out.set(logical, value);
	            }
	        }
	    }
	    return out;
	}
	function viewSectionIndex(view) {
	    const m = view.match(/(\d+)$/);
	    if (m === null) {
	        return 1;
	    }
	    const n = parseInt(m[1], 10);
	    return Number.isFinite(n) && n >= 1 ? n : 1;
	}
	function resolveDesign(page, pageIndex, input, componentByCellName) {
	    const wireStroke = resolveWireStroke(input, page.pageNum);
	    const defaultTextStyle = resolveDefaultTextStyle(input, page.pageNum);
	    const instances = [];
	    let placementSeq = 0;
	    for (const record of (0, AllegroRecordHelpers_1.iterateComponentInstances)(page.body)) {
	        placementSeq++;
	        const resolved = resolveComponentInstance(record, placementSeq, page, input, componentByCellName, wireStroke);
	        if (resolved !== null) {
	            instances.push(resolved);
	        }
	    }
	    const powerSymbols = [];
	    const symbolLabels = [];
	    let powerSeq = 0;
	    for (const symbol of (0, AllegroRecordHelpers_1.iteratePowerSymbols)(page.body)) {
	        powerSeq++;
	        const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(symbol.modelRef);
	        if ((0, AllegroRecordHelpers_1.isOffpageConnectorModelRef)(symbol.modelRef) || isPortCellName(cellName)) {
	            const label = resolveSymbolLabel(symbol, page, input, componentByCellName, wireStroke, defaultTextStyle);
	            if (label !== null) {
	                symbolLabels.push(label);
	            }
	            continue;
	        }
	        const resolved = resolvePowerInstance(symbol, powerSeq, page, input, componentByCellName);
	        if (resolved !== null) {
	            powerSymbols.push(resolved);
	        }
	    }
	    const wireResult = resolveWires(page, input, wireStroke);
	    return {
	        pageNum: page.pageNum,
	        pageFileName: page.pageFileName,
	        sheetName: page.sheetName,
	        sheetId: page.sheetId > 0 ? String(page.sheetId) : null,
	        instances,
	        powerSymbols,
	        symbolLabels,
	        wires: wireResult.wires,
	        buses: wireResult.buses,
	        junctions: wireResult.junctions,
	        defaultTextStyle,
	        wireStroke,
	        busStroke: { width: wireStroke.width * BUS_WIDTH_MULTIPLIER, color: wireStroke.color },
	        graphics: collectGraphics(page),
	        images: collectImages(page),
	        richTexts: collectRichTexts(page),
	        dashedRects: collectDashedRects(page),
	    };
	}
	const PORT_CELL_NAMES = new Set(['ioport', 'inport', 'outport']);
	function isPortCellName(cellName) {
	    return PORT_CELL_NAMES.has(cellName.toLowerCase());
	}
	const GROUND_CELL_NAMES = new Set(['gnd', 'ground']);
	function classifyPowerSymbol(cellName, netName) {
	    if (GROUND_CELL_NAMES.has(cellName.toLowerCase())) {
	        return 'ground';
	    }
	    if (netName !== null && /(^|_)(GND|GROUND)(_|$)/i.test(netName)) {
	        return 'ground';
	    }
	    return 'power';
	}
	function resolveComponentInstance(record, placementSeq, page, input, componentByCellName, wireStroke) {
	    const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(record.modelRef);
	    const view = (0, AllegroRecordHelpers_1.viewOf)(record.modelRef);
	    if (componentByCellName.get(cellName) === undefined) {
	        return null;
	    }
	    const xconInstanceId = input.instanceHashMap.get(`${page.pageNum}:${record.placementHash}`) ?? null;
	    const gateAttrs = xconInstanceId !== null ? (input.instanceAttributes.get(xconInstanceId) ?? null) : null;
	    const dxInstance = xconInstanceId !== null ? (input.parsedDx?.instancesByXconId.get(xconInstanceId) ?? null) : null;
	    const dxRefdes = dxInstance?.attributes.refdes;
	    const dxProps = dxInstance?.properties;
	    const refdes = gateAttrs?.['LOCATION'] ??
	        (dxRefdes !== undefined && dxRefdes.length > 0 ? dxRefdes : undefined) ??
	        `${page.pageFileName}__${placementSeq}__${cellName}`;
	    const moduleRef = `${page.pageFileName}__${placementSeq}__${cellName}`;
	    const value = gateAttrs?.['VALUE'] ?? dxProps?.['VALUE'];
	    const isDnp = gateAttrs?.['SUB'] === 'NP' || dxProps?.['SUB'] === 'NP';
	    const metadata = { ...(dxProps ?? {}), ...(gateAttrs ?? {}) };
	    const center = pickCenter(record, placementSeq);
	    const transform = (0, AllegroTransformModuleGenerator_1.decodeAllegroTransform)((0, AllegroRecordHelpers_1.parseTransformMatrix)(record.transform), (0, AllegroRecordHelpers_1.parseRotationDeg)(record.rotation), null);
	    const component = componentByCellName.get(cellName);
	    const dcfSection = xconInstanceId !== null ? (input.instanceSectionMap.get(xconInstanceId) ?? 1) : 1;
	    const cssView = component?.views.find((v) => v.view === view && v.sectionNumber === dcfSection) ??
	        component?.views.find((v) => v.view === view) ??
	        null;
	    const sectionNumber = cssView?.sectionNumber ?? dcfSection;
	    const instancePins = xconInstanceId !== null ? (input.instancePinMap.get(xconInstanceId) ?? null) : null;
	    const pinNetByName = xconInstanceId !== null ? (input.xconPinNetByInstance.get(xconInstanceId) ?? null) : null;
	    const terminals = [...(0, AllegroRecordHelpers_1.iterateTerminals)(record)];
	    const pins = terminals.map((terminal, idx) => resolvePin(terminal, page, input, {
	        cssView,
	        component: component ?? null,
	        instancePins,
	        pinNetByName,
	        sectionNumber,
	        transform,
	        center,
	        terminalIndex: idx,
	        terminalCount: terminals.length,
	    }));
	    const explicitBodyStroke = resolveExplicitStrokeStyle(input, page.pageNum, record.styleRowIndex, wireStroke);
	    const bodyStroke = explicitBodyStroke.color !== null ? explicitBodyStroke : null;
	    return {
	        xconInstanceId,
	        cellName,
	        view,
	        refdes,
	        moduleRef,
	        value,
	        center,
	        transform,
	        metadata,
	        isDnp,
	        pins,
	        sectionNumber,
	        styleRowIndex: record.styleRowIndex,
	        bodyStroke,
	        rawInstance: { kind: 'component', record, placementSeq },
	    };
	}
	function resolvePowerInstance(symbol, placementSeq, page, input, componentByCellName) {
	    const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(symbol.modelRef);
	    const view = (0, AllegroRecordHelpers_1.viewOf)(symbol.modelRef);
	    if (componentByCellName.get(cellName) === undefined) {
	        return null;
	    }
	    const xconInstanceId = input.instanceHashMap.get(`${page.pageNum}:${symbol.placementHash}`) ?? null;
	    const center = pickPowerCenter(symbol, placementSeq);
	    const transform = (0, AllegroTransformModuleGenerator_1.decodeAllegroTransform)((0, AllegroRecordHelpers_1.parseTransformMatrix)((0, decode_prop_block_1.findPropValue)(symbol.props, 'transform')), (0, AllegroRecordHelpers_1.parseRotationDeg)((0, decode_prop_block_1.findPropValue)(symbol.props, 'rotation')), null);
	    const component = componentByCellName.get(cellName);
	    const cssView = component?.views.find((v) => v.view === view) ?? null;
	    const pinNetByName = xconInstanceId !== null ? (input.xconPinNetByInstance.get(xconInstanceId) ?? null) : null;
	    const associatedNetName = resolveByHash(input, page.pageNum, symbol.associatedNetStubId) ??
	        resolveTerminalNetFallback(symbol, page.pageNum, input);
	    const terminals = [...(0, AllegroRecordHelpers_1.iterateTerminals)(symbol)];
	    const pins = terminals.map((terminal, idx) => resolvePin(terminal, page, input, {
	        cssView,
	        component: component ?? null,
	        instancePins: null,
	        pinNetByName,
	        sectionNumber: 1,
	        transform,
	        center,
	        fallbackNetName: associatedNetName,
	        terminalIndex: idx,
	        terminalCount: terminals.length,
	    }));
	    const refdes = symbol.associatedNetStubId !== null ?
	        `${cellName}__${symbol.associatedNetStubId}`
	        : `${page.pageFileName}__${placementSeq}__${cellName}`;
	    const moduleRef = `${page.pageFileName}__${placementSeq}__${cellName}`;
	    const signalKind = classifyPowerSymbol(cellName, associatedNetName);
	    const signalVoltage = signalKind === 'power' ? parsePowerNetVoltage(associatedNetName, input.netAttributesByName) : undefined;
	    return {
	        xconInstanceId,
	        cellName,
	        view,
	        refdes,
	        moduleRef,
	        value: associatedNetName ?? undefined,
	        center,
	        transform,
	        metadata: {},
	        isDnp: false,
	        pins,
	        sectionNumber: 1,
	        signalKind,
	        signalVoltage,
	        styleRowIndex: symbol.styleRowIndex,
	        bodyStroke: null,
	        rawInstance: { kind: 'power', record: symbol, placementSeq },
	    };
	}
	function parsePowerNetVoltage(netName, netAttributesByName) {
	    if (netName === null) {
	        return undefined;
	    }
	    const voltageAttr = netAttributesByName.get(netName)?.['VOLTAGE'];
	    if (voltageAttr === undefined || voltageAttr.length === 0) {
	        return undefined;
	    }
	    try {
	        return (0, sigma_engine_shared_1.parseNumericalSIValue)(voltageAttr, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions['voltage']);
	    }
	    catch {
	        return undefined;
	    }
	}
	function resolveSymbolLabel(symbol, page, input, componentByCellName, bodyStroke, textStyle) {
	    const xconNetName = resolveByHash(input, page.pageNum, symbol.associatedNetStubId) ??
	        resolveTerminalNetFallback(symbol, page.pageNum, input);
	    const labelText = xconNetName ?? symbol.associatedNetStubId ?? '';
	    if (labelText.length === 0) {
	        return null;
	    }
	    const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(symbol.modelRef);
	    const view = (0, AllegroRecordHelpers_1.viewOf)(symbol.modelRef);
	    const drawPosition = pickPowerCenter(symbol, 0);
	    const termHotpoint = pickFirstTerminalHotpoint(symbol);
	    const transform = (0, AllegroTransformModuleGenerator_1.decodeAllegroTransform)((0, AllegroRecordHelpers_1.parseTransformMatrix)((0, decode_prop_block_1.findPropValue)(symbol.props, 'transform')), (0, AllegroRecordHelpers_1.parseRotationDeg)((0, decode_prop_block_1.findPropValue)(symbol.props, 'rotation')), null);
	    const cssView = componentByCellName.get(cellName)?.views.find((v) => v.view === view) ?? null;
	    const groupDrawings = cssView?.css ? buildSymbolGroupDrawings(cssView.css, transform, bodyStroke, textStyle) : [];
	    return {
	        cellName,
	        view,
	        labelText,
	        netName: xconNetName,
	        combobulatorAnchor: termHotpoint ?? drawPosition,
	        drawPosition,
	        groupOrigin: drawPosition,
	        groupTransform: transform,
	        groupDrawings,
	    };
	}
	function buildSymbolGroupDrawings(css, transform, bodyStroke, textStyle) {
	    const drawings = [];
	    const strokeColor = bodyStroke.color !== null ? { colorOverride: bodyStroke.color } : {};
	    const textColor = textStyle.color !== null ? { colorOverride: textStyle.color } : {};
	    const textAttr = [];
	    if (textStyle.bold) {
	        textAttr.push('bold');
	    }
	    if (textStyle.italic) {
	        textAttr.push('italic');
	    }
	    for (const line of css.lines) {
	        drawings.push({
	            type: 'segment',
	            start: [line.x1 * cssParser_1.CSS_UNIT_TO_MM, -line.y1 * cssParser_1.CSS_UNIT_TO_MM],
	            end: [line.x2 * cssParser_1.CSS_UNIT_TO_MM, -line.y2 * cssParser_1.CSS_UNIT_TO_MM],
	            width: bodyStroke.width,
	            ...strokeColor,
	        });
	    }
	    for (const text of css.texts) {
	        if (text.value === undefined || text.value.length === 0) {
	            continue;
	        }
	        drawings.push({
	            type: 'text',
	            value: text.value,
	            pos: [text.x * cssParser_1.CSS_UNIT_TO_MM, -text.y * cssParser_1.CSS_UNIT_TO_MM],
	            angle: 0,
	            justify: [-1, -1],
	            size: textStyle.size,
	            thickness: 1,
	            ...(textAttr.length > 0 ? { attr: textAttr } : {}),
	            ...textColor,
	        });
	    }
	    if (transform.angle === 0 && !transform.mirrored[0] && !transform.mirrored[1]) {
	        return drawings;
	    }
	    return drawings.map((d) => {
	        const transformed = (0, Transform_1.transformDrawing)(d, {
	            angle: transform.angle,
	            mirrored: transform.mirrored,
	        });
	        if (transformed.type === 'text') {
	            return { ...transformed, justify: rotateJustify(transformed.justify, transform) };
	        }
	        return transformed;
	    });
	}
	function rotateJustify(justify, transform) {
	    let [jx, jy] = justify;
	    if (transform.mirrored[1]) {
	        jx = -jx;
	    }
	    if (transform.mirrored[0]) {
	        jy = -jy;
	    }
	    const angle = ((transform.angle % 360) + 360) % 360;
	    switch (angle) {
	        case 0:
	            return [jx, jy];
	        case 90:
	            return [-jy, jx];
	        case 180:
	            return [-jx, -jy];
	        case 270:
	            return [jy, -jx];
	        default:
	            return [jx, jy];
	    }
	}
	function resolvePin(terminal, page, input, opts) {
	    const rawTerminalName = (0, AllegroRecordHelpers_1.pinNumberFromTerminal)(terminal);
	    const translated = translateDollarPin(rawTerminalName, opts.cssView);
	    const logical = translated === null ? '' : translated.toLowerCase();
	    const refRaw = physicalPinRef(translated, opts.instancePins, opts.component?.chips ?? null, opts.sectionNumber);
	    const ref = opts.component?.isVirtual === true && opts.cssView !== null && refRaw.length > 0 ?
	        `${refRaw}@${opts.cssView.view}`
	        : refRaw;
	    const xconNetName = (logical.length > 0 ? (opts.pinNetByName?.get(logical) ?? null) : null) ??
	        resolveByHash(input, page.pageNum, terminal.netHash) ??
	        opts.fallbackNetName ??
	        null;
	    const cssPin = logical.length > 0 ? (opts.cssView?.pinByLower.get(logical) ?? null) : null;
	    const symbolLocalPosition = cssPin !== null ? [cssPin.hotX * cssParser_1.CSS_UNIT_TO_MM, -cssPin.hotY * cssParser_1.CSS_UNIT_TO_MM] : null;
	    const hotpoint = (0, AllegroRecordHelpers_1.terminalHotpointMm)(terminal);
	    const absolutePosition = hotpoint !== null ? [hotpoint.x, hotpoint.y]
	        : symbolLocalPosition !== null ? applyPlacementTransform(symbolLocalPosition, opts.transform, opts.center)
	            : applyPlacementTransform(placeholderPinLocalPosition(opts.terminalIndex, opts.terminalCount), opts.transform, opts.center);
	    return { ref, logical, netName: xconNetName, symbolLocalPosition, absolutePosition };
	}
	const PLACEHOLDER_HALF_WIDTH = 2.5;
	const PLACEHOLDER_PIN_LENGTH = 1;
	const PLACEHOLDER_PIN_SPACING = 2.5;
	function placeholderPinLocalPosition(termIndex, totalTerms) {
	    const sideCount = Math.ceil(Math.max(totalTerms, 1) / 2);
	    const onLeftSide = termIndex % 2 === 0;
	    const sideIndex = Math.floor(termIndex / 2);
	    const verticalSpan = (sideCount - 1) * PLACEHOLDER_PIN_SPACING;
	    const y = -verticalSpan / 2 + sideIndex * PLACEHOLDER_PIN_SPACING;
	    const x = onLeftSide ? -(PLACEHOLDER_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH) : PLACEHOLDER_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH;
	    return [x, y];
	}
	function translateDollarPin(rawName, cssView) {
	    if (rawName === null) {
	        return null;
	    }
	    const match = /^\$pin(\d+)$/i.exec(rawName);
	    if (match === null || cssView === null) {
	        return rawName;
	    }
	    const idx = parseInt(match[1], 10);
	    const mapped = cssView.cssPinRefByOrdinal[idx];
	    return mapped !== undefined && mapped.length > 0 ? mapped : rawName;
	}
	function physicalPinRef(logicalRaw, instancePins, chips, sectionNumber) {
	    if (logicalRaw === null) {
	        return '';
	    }
	    if (instancePins !== null) {
	        const pn = instancePins.get(logicalRaw);
	        if (pn !== undefined && pn.length > 0) {
	            return pn;
	        }
	    }
	    if (chips !== null) {
	        const physical = chips.pinNumberByName.get(logicalRaw.toLowerCase());
	        if (physical !== undefined && physical.length > 0) {
	            if (physical.includes(',')) {
	                const parts = physical.split(',').map((p) => p.trim());
	                const idx = sectionNumber - 1;
	                const selected = parts[idx] ?? parts[0];
	                return selected === '0' ? logicalRaw.toLowerCase() : selected;
	            }
	            return physical;
	        }
	    }
	    return logicalRaw.toLowerCase();
	}
	function applyPlacementTransform(local, transform, center) {
	    const dx = (transform.mirrored[0] ? -1 : 1) * local[0];
	    const dy = (transform.mirrored[1] ? -1 : 1) * local[1];
	    const rad = (transform.angle * Math.PI) / 180;
	    const cosA = Math.cos(rad);
	    const sinA = Math.sin(rad);
	    return [center[0] + cosA * dx + sinA * dy, center[1] - sinA * dx + cosA * dy];
	}
	function pickCenter(record, placementSeq) {
	    if (record.position !== null) {
	        return [record.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, record.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM];
	    }
	    const bbox = (0, AllegroRecordHelpers_1.bboxToMm)(record.bbox);
	    if (bbox !== null && (bbox.x1 !== 0 || bbox.y1 !== 0 || bbox.x2 !== 0 || bbox.y2 !== 0)) {
	        return [(bbox.x1 + bbox.x2) / 2, (bbox.y1 + bbox.y2) / 2];
	    }
	    return gridLayoutCenter(placementSeq);
	}
	function pickPowerCenter(symbol, placementSeq) {
	    if (symbol.position !== null) {
	        return [symbol.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, symbol.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM];
	    }
	    const bbox = (0, AllegroRecordHelpers_1.bboxToMm)(symbol.bbox);
	    if (bbox !== null && (bbox.x1 !== 0 || bbox.y1 !== 0 || bbox.x2 !== 0 || bbox.y2 !== 0)) {
	        return [(bbox.x1 + bbox.x2) / 2, (bbox.y1 + bbox.y2) / 2];
	    }
	    return gridLayoutCenter(placementSeq);
	}
	function gridLayoutCenter(placementSeq) {
	    const idx = placementSeq - 1;
	    const col = idx % GRID_COLUMNS;
	    const row = Math.floor(idx / GRID_COLUMNS);
	    return [col * GRID_SPACING, -row * GRID_SPACING];
	}
	function pickFirstTerminalHotpoint(symbol) {
	    for (const terminal of (0, AllegroRecordHelpers_1.iterateTerminals)(symbol)) {
	        const hp = (0, AllegroRecordHelpers_1.terminalHotpointMm)(terminal);
	        if (hp !== null) {
	            return [hp.x, hp.y];
	        }
	    }
	    return null;
	}
	function resolveByHash(input, pageNum, hash) {
	    if (pageNum === null || hash === null) {
	        return null;
	    }
	    const netId = input.netHashMap.get(`${pageNum}:${hash}`);
	    if (netId === undefined) {
	        return null;
	    }
	    return resolveNetIdToName(input, netId);
	}
	function resolveNetIdToName(input, netId) {
	    const phys = input.physNetNameByXconId.get(netId);
	    if (phys !== undefined) {
	        return phys;
	    }
	    const xconNet = input.parsedXcon?.designs[0]?.nets.get(netId);
	    return xconNet?.name ?? null;
	}
	function resolveTerminalNetFallback(symbol, pageNum, input) {
	    for (const terminal of (0, AllegroRecordHelpers_1.iterateTerminals)(symbol)) {
	        const name = resolveByHash(input, pageNum, terminal.netHash);
	        if (name !== null) {
	            return name;
	        }
	    }
	    return null;
	}
	function resolveWires(page, input, wireStroke) {
	    const wires = [];
	    const buses = [];
	    const endpointCounts = new Map();
	    for (const stub of (0, AllegroRecordHelpers_1.iterateNetStubs)(page.body)) {
	        if (stub.isDefault) {
	            continue;
	        }
	        const netName = resolveByHash(input, page.pageNum, stub.stubId);
	        for (const bundle of (0, AllegroRecordHelpers_1.iterateWireBundles)(stub)) {
	            const isBus = bundle.isBus;
	            const bundleStroke = resolveBundleStroke(input, page.pageNum, bundle.styleRowIndex, wireStroke);
	            for (const segment of (0, AllegroRecordHelpers_1.iterateWireSegments)(bundle)) {
	                if (segment.startCoord === null || segment.endCoord === null) {
	                    continue;
	                }
	                const from = [
	                    segment.startCoord.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	                    segment.startCoord.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	                ];
	                const to = [
	                    segment.endCoord.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	                    segment.endCoord.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	                ];
	                if (isBus) {
	                    buses.push({ from, to, stroke: bundleStroke });
	                }
	                else {
	                    wires.push({ from, to, netName, isBus: false, stroke: bundleStroke });
	                }
	                addEndpoint(endpointCounts, from);
	                addEndpoint(endpointCounts, to);
	            }
	        }
	    }
	    const junctions = [];
	    for (const { x, y, count } of endpointCounts.values()) {
	        if (count >= 3) {
	            junctions.push({ position: [x, y] });
	        }
	    }
	    return { wires, buses, junctions };
	}
	function resolveWireStroke(input, pageNum) {
	    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${WIRE_STYLE_ROW_INDEX}`);
	    const style = styleName !== undefined ? input.styles.design.get(styleName) : undefined;
	    return strokeFromStyle(style, 'sheet-default');
	}
	function resolveBundleStroke(input, pageNum, rowIndex, wireStroke) {
	    return resolveExplicitStrokeStyle(input, pageNum, rowIndex, wireStroke);
	}
	function resolveExplicitStrokeStyle(input, pageNum, rowIndex, fallback) {
	    if (rowIndex === null) {
	        return fallback;
	    }
	    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${rowIndex}`);
	    if (styleName === undefined) {
	        return fallback;
	    }
	    const style = input.styles.design.get(styleName);
	    if (style === undefined) {
	        return fallback;
	    }
	    const width = style.lineWidth !== null && style.lineWidth > 0 ? style.lineWidth * STYLE_LINE_WIDTH_TO_MM : fallback.width;
	    const color = style.lineColor !== null ? (hexToColorOverride(style.lineColor) ?? fallback.color) : fallback.color;
	    return { width, color };
	}
	function resolveExplicitTextStyle(input, pageNum, rowIndex, fontSizeOverride, fallback) {
	    if (rowIndex === null) {
	        return fallback;
	    }
	    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${rowIndex}`);
	    if (styleName === undefined) {
	        return fallback;
	    }
	    const style = input.styles.design.get(styleName);
	    if (style === undefined) {
	        return fallback;
	    }
	    const size = fontSizeOverride !== null && fontSizeOverride > 0 ? fontSizeOverride * STYLE_FONT_SIZE_TO_MM
	        : style.fontSize !== null && style.fontSize > 0 ? style.fontSize * STYLE_FONT_SIZE_TO_MM
	            : fallback.size;
	    const color = style.fontColor !== null ? (hexToColorOverride(style.fontColor) ?? fallback.color) : fallback.color;
	    return {
	        size,
	        color,
	        font: style.fontName ?? fallback.font,
	        italic: style.italic === null ? fallback.italic : style.italic,
	        bold: style.bold === null ? fallback.bold : style.bold,
	    };
	}
	function resolveDefaultTextStyle(input, pageNum) {
	    const styleName = input.styleAssignmentByKey.get(`${pageNum}:${WIRE_STYLE_ROW_INDEX}`);
	    const style = styleName !== undefined ? input.styles.design.get(styleName) : undefined;
	    return textStyleFromProjectStyle(style, style?.fontSize ?? null, 'sheet-default');
	}
	function strokeFromStyle(style, source) {
	    const width = style !== undefined && style.lineWidth !== null && style.lineWidth > 0 ?
	        style.lineWidth * STYLE_LINE_WIDTH_TO_MM
	        : DEFAULT_WIRE_WIDTH_MM;
	    const color = source === 'explicit' && style !== undefined ? hexToColorOverride(style.lineColor) : null;
	    return { width, color };
	}
	function textStyleFromProjectStyle(style, fontSizeOverride, source) {
	    const fontSize = fontSizeOverride !== null && fontSizeOverride > 0 ? fontSizeOverride
	        : style !== undefined && style.fontSize !== null && style.fontSize > 0 ? style.fontSize
	            : null;
	    const size = fontSize !== null ? fontSize * STYLE_FONT_SIZE_TO_MM : DEFAULT_TEXT_SIZE_MM;
	    const color = source === 'explicit' && style !== undefined ? hexToColorOverride(style.fontColor) : null;
	    return {
	        size,
	        color,
	        font: style?.fontName ?? null,
	        italic: style?.italic === true,
	        bold: style?.bold === true,
	    };
	}
	const STYLE_DEFAULT_COLOR = '#000000';
	function hexToColorOverride(hex) {
	    if (hex === null) {
	        return null;
	    }
	    const trimmed = hex.trim();
	    if (trimmed.toLowerCase() === STYLE_DEFAULT_COLOR) {
	        return null;
	    }
	    const m = /^#([0-9a-fA-F]{6})$/.exec(trimmed);
	    if (m === null) {
	        return null;
	    }
	    const r = parseInt(m[1].slice(0, 2), 16);
	    const g = parseInt(m[1].slice(2, 4), 16);
	    const b = parseInt(m[1].slice(4, 6), 16);
	    return [r, g, b, 1];
	}
	function addEndpoint(counts, point) {
	    const qx = Math.round(point[0] * JUNCTION_QUANTIZE);
	    const qy = Math.round(point[1] * JUNCTION_QUANTIZE);
	    const key = `${qx},${qy}`;
	    const existing = counts.get(key);
	    if (existing === undefined) {
	        counts.set(key, { x: qx / JUNCTION_QUANTIZE, y: qy / JUNCTION_QUANTIZE, count: 1 });
	    }
	    else {
	        existing.count++;
	    }
	}
	function collectGraphics(page) {
	    const slot = page.body.slots.graphics;
	    if (slot === null) {
	        return [];
	    }
	    return [...(0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')];
	}
	function collectRichTexts(page) {
	    const slot = page.body.slots.graphics;
	    if (slot === null) {
	        return [];
	    }
	    const out = [];
	    for (const item of (0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')) {
	        if (item.subShape !== 'standard-html-annotation') {
	            continue;
	        }
	        const richText = resolveRichTextAnnotation(item);
	        if (richText !== null) {
	            out.push(richText);
	        }
	    }
	    return out;
	}
	function resolveRichTextAnnotation(item) {
	    const raw = item.raw;
	    if (raw.type !== 'container' && raw.type !== 'econtainer') {
	        return null;
	    }
	    const payload = readZeronullHtmlPayload(raw.children);
	    if (payload === null || payload.length === 0) {
	        return null;
	    }
	    const extent = readImageExtent(raw.children);
	    const positionNode = findPositionContainer(raw.children);
	    if (extent === null || positionNode === null) {
	        return null;
	    }
	    const position = readImagePosition(positionNode);
	    if (position === null) {
	        return null;
	    }
	    const left = position.x + extent.minX;
	    const top = position.y + extent.minY;
	    const parsed = parseQRichText(payload);
	    if (parsed.paragraphs.length === 0) {
	        return null;
	    }
	    return {
	        pos: [left * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, top * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM],
	        width: (extent.maxX - extent.minX) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	        height: (extent.maxY - extent.minY) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	        paragraphs: parsed.paragraphs,
	        defaultSizePt: parsed.defaultSizePt,
	        defaultBold: parsed.defaultBold,
	        defaultItalic: parsed.defaultItalic,
	        defaultFont: parsed.defaultFont,
	    };
	}
	function readZeronullHtmlPayload(children) {
	    for (let i = 0; i + 3 < children.length; i++) {
	        const c = children[i];
	        const next3 = children[i + 3];
	        if (c?.type === 'atom' && c.content === 'zeronull' && next3?.type === 'atom' && typeof next3.content === 'string') {
	            return next3.content;
	        }
	    }
	    return null;
	}
	function parseQRichText(html) {
	    const defaults = parseBodyDefaults(html);
	    const body = extractBodyInner(html);
	    const paragraphs = [];
	    const pTagRe = /<p\b([^>]*)>([\s\S]*?)<\/p\s*>/gi;
	    let match;
	    while ((match = pTagRe.exec(body)) !== null) {
	        const inner = match[2];
	        const segments = inner.split(/<br\s*\/?\s*>/i);
	        for (const segment of segments) {
	            const runs = parseRunsFromSegment(segment);
	            paragraphs.push({ runs });
	        }
	    }
	    return {
	        paragraphs,
	        defaultSizePt: defaults.sizePt,
	        defaultBold: defaults.bold,
	        defaultItalic: defaults.italic,
	        defaultFont: defaults.font,
	    };
	}
	function parseRunsFromSegment(segment) {
	    const runs = [];
	    const tokenRe = /<span\b([^>]*)>([\s\S]*?)<\/span\s*>|([^<]+)/gi;
	    let match;
	    while ((match = tokenRe.exec(segment)) !== null) {
	        if (match[3] !== undefined) {
	            const text = decodeEntities(match[3]);
	            if (text.length > 0) {
	                runs.push({ value: text });
	            }
	        }
	        else {
	            const style = parseStyleAttr(match[1]);
	            const text = decodeEntities(stripInlineTags(match[2]));
	            if (text.length === 0) {
	                continue;
	            }
	            runs.push({
	                value: text,
	                bold: style.bold,
	                italic: style.italic,
	                sizePt: style.sizePt,
	                font: style.font,
	            });
	        }
	    }
	    return runs;
	}
	function parseBodyDefaults(html) {
	    const bodyOpen = /<body\b([^>]*)>/i.exec(html);
	    if (bodyOpen === null) {
	        return {};
	    }
	    const styleAttr = /style\s*=\s*("([^"]*)"|'([^']*)')/i.exec(bodyOpen[1]);
	    if (styleAttr === null) {
	        return {};
	    }
	    return parseStyleString(styleAttr[2] ?? styleAttr[3] ?? '');
	}
	function extractBodyInner(html) {
	    const m = /<body\b[^>]*>([\s\S]*?)<\/body\s*>/i.exec(html);
	    return m === null ? html : m[1];
	}
	function parseStyleAttr(tagAttrs) {
	    const styleAttr = /style\s*=\s*("([^"]*)"|'([^']*)')/i.exec(tagAttrs);
	    if (styleAttr === null) {
	        return {};
	    }
	    return parseStyleString(styleAttr[2] ?? styleAttr[3] ?? '');
	}
	function parseStyleString(style) {
	    const out = {};
	    for (const decl of style.split(';')) {
	        const colon = decl.indexOf(':');
	        if (colon < 0) {
	            continue;
	        }
	        const key = decl.slice(0, colon).trim().toLowerCase();
	        const value = decl.slice(colon + 1).trim();
	        if (key === 'font-size') {
	            const ptMatch = /^(\d+(?:\.\d+)?)\s*pt$/i.exec(value);
	            if (ptMatch !== null) {
	                const n = Number.parseFloat(ptMatch[1]);
	                if (Number.isFinite(n) && n > 0) {
	                    out.sizePt = n;
	                }
	            }
	        }
	        else if (key === 'font-weight') {
	            const lower = value.toLowerCase();
	            if (lower === 'bold' || lower === 'bolder') {
	                out.bold = true;
	            }
	            else {
	                const n = Number.parseInt(lower, 10);
	                if (Number.isFinite(n) && n >= 600) {
	                    out.bold = true;
	                }
	            }
	        }
	        else if (key === 'font-style') {
	            const lower = value.toLowerCase();
	            if (lower === 'italic' || lower === 'oblique') {
	                out.italic = true;
	            }
	        }
	        else if (key === 'font-family') {
	            const first = value
	                .split(',')[0]
	                .trim()
	                .replace(/^['"]|['"]$/g, '');
	            if (first.length > 0) {
	                out.font = first;
	            }
	        }
	    }
	    return out;
	}
	function stripInlineTags(content) {
	    return content.replace(/<[^>]*>/g, '');
	}
	function decodeEntities(text) {
	    return text
	        .replace(/&amp;/g, '&')
	        .replace(/&lt;/g, '<')
	        .replace(/&gt;/g, '>')
	        .replace(/&quot;/g, '"')
	        .replace(/&#39;/g, "'")
	        .replace(/&nbsp;/g, ' ');
	}
	function collectDashedRects(page) {
	    const slot = page.body.slots.graphics;
	    if (slot === null) {
	        return [];
	    }
	    const out = [];
	    for (const item of (0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')) {
	        if (item.subShape !== 'generic-graphic-no-cgtype') {
	            continue;
	        }
	        const rect = resolveDashedRect(item);
	        if (rect !== null) {
	            out.push(rect);
	        }
	    }
	    return out;
	}
	function resolveDashedRect(item) {
	    const raw = item.raw;
	    if (raw.type !== 'container' && raw.type !== 'econtainer') {
	        return null;
	    }
	    const extent = readImageExtent(raw.children);
	    const positionNode = findPositionContainer(raw.children);
	    if (extent === null || positionNode === null) {
	        return null;
	    }
	    const position = readImagePosition(positionNode);
	    if (position === null) {
	        return null;
	    }
	    const left = position.x + extent.minX;
	    const top = position.y + extent.minY;
	    return {
	        pos: [left * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, top * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM],
	        width: (extent.maxX - extent.minX) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	        height: (extent.maxY - extent.minY) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	    };
	}
	function collectImages(page) {
	    const slot = page.body.slots.graphics;
	    if (slot === null) {
	        return [];
	    }
	    const out = [];
	    for (const item of (0, AllegroRecordHelpers_1.entityItems)(slot, 'graphics-annotation')) {
	        if (item.subShape !== 'image-annotation') {
	            continue;
	        }
	        const image = resolveImagePlacement(item);
	        if (image !== null) {
	            out.push(image);
	        }
	    }
	    return out;
	}
	function resolveImagePlacement(item) {
	    const filename = (0, decode_prop_block_1.findPropValue)(item.props, 'GRAPHICS_IMAGE_FILE');
	    if (filename === null || filename.length === 0) {
	        return null;
	    }
	    const raw = item.raw;
	    if (raw.type !== 'container' && raw.type !== 'econtainer') {
	        return null;
	    }
	    const extent = readImageExtent(raw.children);
	    const positionNode = findPositionContainer(raw.children);
	    if (extent === null || positionNode === null) {
	        return null;
	    }
	    const position = readImagePosition(positionNode);
	    if (position === null) {
	        return null;
	    }
	    const angleStr = (0, decode_prop_block_1.findPropValue)(item.props, 'rotation');
	    const angle = angleStr !== null ? Number.parseFloat(angleStr) : 0;
	    const left = position.x + extent.minX;
	    const top = position.y + extent.minY;
	    return {
	        filename,
	        pos: [left * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM, top * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM],
	        width: (extent.maxX - extent.minX) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	        height: (extent.maxY - extent.minY) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	        angle: Number.isFinite(angle) ? angle : 0,
	    };
	}
	function readImageExtent(children) {
	    for (const child of children) {
	        if (child.type !== 'container' && child.type !== 'econtainer') {
	            continue;
	        }
	        const inner = child.children;
	        if (!Array.isArray(inner)) {
	            continue;
	        }
	        const corners = [];
	        for (const grand of inner) {
	            const g = grand;
	            if (g.type !== 'container' && g.type !== 'econtainer') {
	                continue;
	            }
	            const pos = (0, decode_tc31_visual_label_1.readCadencePosition)(grand);
	            if (pos !== null) {
	                corners.push(pos);
	            }
	        }
	        if (corners.length >= 2) {
	            let minX = corners[0].x;
	            let maxX = corners[0].x;
	            let minY = corners[0].y;
	            let maxY = corners[0].y;
	            for (let i = 1; i < corners.length; i++) {
	                const c = corners[i];
	                if (c.x < minX) {
	                    minX = c.x;
	                }
	                if (c.x > maxX) {
	                    maxX = c.x;
	                }
	                if (c.y < minY) {
	                    minY = c.y;
	                }
	                if (c.y > maxY) {
	                    maxY = c.y;
	                }
	            }
	            return { minX, minY, maxX, maxY };
	        }
	    }
	    return null;
	}
	function findPositionContainer(children) {
	    for (const child of children) {
	        if (child.type !== 'container' && child.type !== 'econtainer') {
	            continue;
	        }
	        const inner = child.children;
	        if (!Array.isArray(inner) || inner.length !== 4) {
	            continue;
	        }
	        if (inner.every((g) => g.type === 'atom')) {
	            return child;
	        }
	    }
	    return null;
	}
	function readImagePosition(node) {
	    return (0, decode_tc31_visual_label_1.readCadencePosition)(node);
	}
	
	return AllegroResolveDesign;
}

var hasRequiredAllegroTransformModuleGenerator;

function requireAllegroTransformModuleGenerator () {
	if (hasRequiredAllegroTransformModuleGenerator) return AllegroTransformModuleGenerator;
	hasRequiredAllegroTransformModuleGenerator = 1;
	Object.defineProperty(AllegroTransformModuleGenerator, "__esModule", { value: true });
	AllegroTransformModuleGenerator.AllegroTransformModuleGenerator = void 0;
	AllegroTransformModuleGenerator.decodeAllegroTransform = decodeAllegroTransform;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformObject_1 = requireTransformObject();
	const TransformProject_1 = requireTransformProject();
	const TransformProjectModule_1 = requireTransformProjectModule();
	const TransformModule_1 = requireTransformModule();
	const TransformModulePinConnection_1 = requireTransformModulePinConnection();
	const TransformSchematic_1 = requireTransformSchematic();
	const allegroLayers_1 = requireAllegroLayers();
	const decode_prop_block_1 = requireDecodePropBlock();
	const AllegroResolved_1 = requireAllegroResolved();
	const AllegroRecordHelpers_1 = requireAllegroRecordHelpers();
	const AllegroResolveDesign_1 = requireAllegroResolveDesign();
	const { SchLayerType } = sigma_engine_shared_1.DesignModels;
	const NAME_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.NameLabel].id;
	const VALUE_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.ValueLabel].id;
	class AllegroTransformModuleGenerator$1 {
	    constructor() {
	        this.runsFor = [
	            TransformSchematic_1.TransformSchematic,
	        ];
	    }
	    *generate(schematic, rootContext) {
	        const project = TransformObject_1.TransformObject.FindParent(schematic, TransformProject_1.TransformProject);
	        if (project === null) {
	            return;
	        }
	        const pageNum = schematic.instance.hierarchyEntry.loaded.pageNum;
	        const design = rootContext.resolved.designs.find((d) => d.pageNum === pageNum);
	        if (design === undefined) {
	            return;
	        }
	        const instPropTextStyle = rootContext.resolved.instPropTextStyle;
	        for (const instance of design.instances) {
	            yield* emit(schematic, project, rootContext, instance, instPropTextStyle, pageNum);
	        }
	    }
	}
	AllegroTransformModuleGenerator.AllegroTransformModuleGenerator = AllegroTransformModuleGenerator$1;
	function* emit(schematic, project, rootContext, resolved, textStyle, pageNum) {
	    const component = rootContext.componentsByCellName.get(resolved.cellName);
	    if (component === undefined || resolved.rawInstance.kind !== 'component') {
	        return;
	    }
	    const record = resolved.rawInstance.record;
	    const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
	        name: resolved.refdes,
	        component,
	        value: resolved.value,
	        metadata: Object.keys(resolved.metadata).length > 0 ? resolved.metadata : undefined,
	        isDnp: resolved.isDnp,
	    });
	    yield projectModule;
	    const bodyColorOverride = resolved.bodyStroke?.color ?? undefined;
	    const moduleProps = {
	        ref: resolved.moduleRef,
	        footprintRef: (0, AllegroResolved_1.allegroSymbolRefForCell)(resolved.cellName, resolved.view, resolved.sectionNumber),
	        center: resolved.center,
	        transform: resolved.transform,
	        name: resolved.refdes,
	        drawProperties: buildModuleDrawProperties(resolved, record, textStyle, rootContext, pageNum),
	        ...(bodyColorOverride !== undefined ? { bodyColorOverride } : {}),
	    };
	    yield new TransformModule_1.TransformModule(schematic, moduleProps, component, projectModule);
	    for (const pin of resolved.pins) {
	        yield new TransformModulePinConnection_1.TransformModulePinConnection(schematic, {
	            position: pin.absolutePosition,
	            connection: {
	                moduleRef: resolved.moduleRef,
	                moduleName: resolved.refdes,
	                pinRef: pin.ref,
	                skipPositionalCombobulation: true,
	                appliesNetName: pin.netName !== null ?
	                    { label: pin.netName, labelType: 'system', strategyMetadata: { xconNetName: pin.netName } }
	                    : null,
	            },
	        });
	    }
	}
	const TALL_SYMBOL_ASPECT_THRESHOLD = 3;
	function decodeAllegroTransform(matrix, rotationDeg = null, cssBbox = null) {
	    let m00 = 1;
	    let m01 = 0;
	    let m10 = 0;
	    let m11 = 1;
	    if (matrix !== null && matrix.length >= 9) {
	        m00 = matrix[0];
	        m01 = matrix[1];
	        m10 = matrix[3];
	        m11 = matrix[4];
	    }
	    const det = m00 * m11 - m01 * m10;
	    const mirroredX = det < 0;
	    const a00 = mirroredX ? -m00 : m00;
	    const a10 = mirroredX ? -m10 : m10;
	    const matrixAngle = (Math.atan2(a10, a00) * 180) / Math.PI;
	    const isExtremelyTall = cssBbox !== null && cssBbox.w > 0 && cssBbox.h / cssBbox.w >= TALL_SYMBOL_ASPECT_THRESHOLD;
	    const rawRotation = rotationDeg ?? 0;
	    const effectiveRotation = rawRotation > 0 && isExtremelyTall ? 0 : rawRotation;
	    let angleDeg = -(matrixAngle + effectiveRotation);
	    angleDeg = Math.round(angleDeg / 90) * 90;
	    angleDeg = ((angleDeg % 360) + 360) % 360;
	    return { angle: angleDeg, mirrored: [false, mirroredX] };
	}
	function buildModuleDrawProperties(resolved, record, sheetTextStyle, rootContext, pageNum) {
	    const props = {};
	    const { center, metadata } = resolved;
	    const { angle: transformAngle, mirrored } = resolved.transform;
	    let hasVisualLabels = false;
	    for (const label of (0, AllegroRecordHelpers_1.iterateVisualLabels)(record)) {
	        const propName = label.propertyName;
	        if (propName === null || label.position === null) {
	            continue;
	        }
	        hasVisualLabels = true;
	        const upperName = propName.toUpperCase();
	        const attrValue = metadata[upperName] ?? metadata[propName];
	        if (attrValue === undefined || attrValue.length === 0) {
	            continue;
	        }
	        const rendererKey = upperName === 'LOCATION' ? 'Part Reference'
	            : upperName === 'VALUE' ? 'Value'
	                : propName;
	        const rad = (transformAngle * Math.PI) / 180;
	        const cosA = Math.cos(rad);
	        const sinA = Math.sin(rad);
	        const dx = (mirrored[0] ? -1 : 1) * label.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM;
	        const dy = label.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM;
	        const pos = [center[0] + cosA * dx + sinA * dy, center[1] - sinA * dx + cosA * dy];
	        const isRefdes = upperName === 'LOCATION';
	        const isValue = upperName === 'VALUE';
	        const layer = isRefdes ? NAME_LABEL_LAYER : VALUE_LABEL_LAYER;
	        const rawJustify = cadenceJustToXY((0, decode_prop_block_1.findPropValue)(label.props, 'just'));
	        const rjx = Math.round(cosA * rawJustify[0] + sinA * rawJustify[1]);
	        const rjy = Math.round(-sinA * rawJustify[0] + cosA * rawJustify[1]);
	        const justify = mirrored[0] ? [-rjx, rjy] : [rjx, rjy];
	        const mirror = parseLabelMirror((0, decode_prop_block_1.findPropValue)(label.props, 'transform'));
	        const primaryStyle = (0, AllegroResolveDesign_1.resolveExplicitTextStyle)(rootContext, pageNum, label.styleRowIndex, null, sheetTextStyle);
	        const labelStyle = (0, AllegroResolveDesign_1.resolveExplicitTextStyle)(rootContext, pageNum, label.secondaryStyleRowIndex, null, primaryStyle);
	        const labelColor = {};
	        const labelAttr = [];
	        if (labelStyle.bold) {
	            labelAttr.push('bold');
	        }
	        if (labelStyle.italic) {
	            labelAttr.push('italic');
	        }
	        const labelAttrField = labelAttr.length > 0 ? { attr: labelAttr } : {};
	        props[rendererKey] = {
	            value: attrValue,
	            pos,
	            layer,
	            angle: 0,
	            justify,
	            size: labelStyle.size,
	            thickness: 1,
	            hidden: false,
	            ...labelColor,
	            ...labelAttrField,
	            ...(isRefdes ? { ref_designator: true } : {}),
	            ...(isValue ? { val: true } : {}),
	            mirror,
	        };
	    }
	    if (!hasVisualLabels) {
	        const sheetColor = sheetTextStyle.color !== null ? { colorOverride: sheetTextStyle.color } : {};
	        const sheetAttr = [];
	        if (sheetTextStyle.bold) {
	            sheetAttr.push('bold');
	        }
	        if (sheetTextStyle.italic) {
	            sheetAttr.push('italic');
	        }
	        const sheetAttrField = sheetAttr.length > 0 ? { attr: sheetAttr } : {};
	        const halfH = parseCdsLmanHalfHeight(metadata['CDS_LMAN_SYM_OUTLINE']);
	        const offset = halfH + sheetTextStyle.size;
	        props['Part Reference'] = {
	            value: resolved.refdes,
	            pos: [center[0], center[1] + offset],
	            layer: NAME_LABEL_LAYER,
	            angle: 0,
	            justify: [-1, -1],
	            size: sheetTextStyle.size,
	            thickness: 1,
	            hidden: false,
	            ...sheetColor,
	            ...sheetAttrField,
	            ref_designator: true,
	            mirror: [false, false],
	        };
	        if (resolved.value !== undefined && resolved.value.length > 0) {
	            props['Value'] = {
	                value: resolved.value,
	                pos: [center[0], center[1] - offset],
	                layer: VALUE_LABEL_LAYER,
	                angle: 0,
	                justify: [-1, -1],
	                size: sheetTextStyle.size,
	                thickness: 1,
	                hidden: false,
	                ...sheetColor,
	                ...sheetAttrField,
	                val: true,
	                mirror: [false, false],
	            };
	        }
	    }
	    return props;
	}
	function cadenceJustToXY(raw) {
	    const n = raw !== null ? parseInt(raw, 10) : NaN;
	    if (!Number.isFinite(n) || n < 0 || n > 8) {
	        return [-1, -1];
	    }
	    const col = n % 3;
	    const row = Math.floor(n / 3);
	    const x = col === 0 ? -1
	        : col === 1 ? 0
	            : 1;
	    const y = row === 0 ? -1
	        : row === 1 ? 0
	            : 1;
	    return [x, y];
	}
	function parseLabelMirror(transform) {
	    const matrix = (0, AllegroRecordHelpers_1.parseTransformMatrix)(transform);
	    if (matrix === null || matrix.length < 5) {
	        return [false, false];
	    }
	    const det = matrix[0] * matrix[4] - matrix[1] * matrix[3];
	    return [det < 0, false];
	}
	const SYM_OUTLINE_FALLBACK_HALF_HEIGHT_MM = 6.0;
	function parseCdsLmanHalfHeight(raw) {
	    const FALLBACK = SYM_OUTLINE_FALLBACK_HALF_HEIGHT_MM;
	    if (raw === undefined || raw.length === 0) {
	        return FALLBACK;
	    }
	    const parts = raw.split(',').map(Number);
	    if (parts.length < 4 || parts.some((n) => !Number.isFinite(n))) {
	        return FALLBACK;
	    }
	    const [, y1, , y2] = parts;
	    const h = Math.abs(y2 - y1) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM;
	    return h > 0 ? h / 2 : FALLBACK;
	}
	
	return AllegroTransformModuleGenerator;
}

var AllegroTransformNetWireGenerator = {};

var hasRequiredAllegroTransformNetWireGenerator;

function requireAllegroTransformNetWireGenerator () {
	if (hasRequiredAllegroTransformNetWireGenerator) return AllegroTransformNetWireGenerator;
	hasRequiredAllegroTransformNetWireGenerator = 1;
	Object.defineProperty(AllegroTransformNetWireGenerator, "__esModule", { value: true });
	AllegroTransformNetWireGenerator.AllegroTransformNetWireGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformNetWire_1 = requireTransformNetWire();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	const TransformSchematic_1 = requireTransformSchematic();
	const allegroLayers_1 = requireAllegroLayers();
	const WIRE_LAYER = allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Wire].id;
	const BUS_LAYER = allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Bus].id;
	const JUNCTION_LAYER = allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Junction].id;
	class AllegroTransformNetWireGenerator$1 {
	    constructor() {
	        this.runsFor = [
	            TransformSchematic_1.TransformSchematic,
	        ];
	    }
	    *generate(schematic, rootContext) {
	        const pageNum = schematic.instance.hierarchyEntry.loaded.pageNum;
	        const design = rootContext.resolved.designs.find((d) => d.pageNum === pageNum);
	        if (design === undefined) {
	            return;
	        }
	        for (const wire of design.wires) {
	            const stroke = wire.stroke;
	            const drawing = {
	                layers: [WIRE_LAYER],
	                drawing: {
	                    type: 'segment',
	                    start: wire.from,
	                    end: wire.to,
	                    width: stroke.width,
	                    ...(stroke.color !== null ? { colorOverride: stroke.color } : {}),
	                },
	            };
	            const strategyMetadata = wire.netName !== null ? { xconNetName: wire.netName } : undefined;
	            yield new TransformNetWire_1.TransformNetWire(schematic, wire.from, wire.to, drawing, undefined, undefined, strategyMetadata);
	        }
	        for (const bus of design.buses) {
	            const stroke = bus.stroke;
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(schematic, {
	                layers: [BUS_LAYER],
	                drawing: {
	                    type: 'segment',
	                    start: bus.from,
	                    end: bus.to,
	                    width: stroke.width,
	                    ...(stroke.color !== null ? { colorOverride: stroke.color } : {}),
	                },
	            });
	        }
	        for (const junction of design.junctions) {
	            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(schematic, {
	                layers: [JUNCTION_LAYER],
	                drawing: { type: 'junction', position: junction.position, radiusOverride: 0.5 },
	            });
	        }
	    }
	}
	AllegroTransformNetWireGenerator.AllegroTransformNetWireGenerator = AllegroTransformNetWireGenerator$1;
	
	return AllegroTransformNetWireGenerator;
}

var AllegroTransformOffPageConnectorGenerator = {};

var hasRequiredAllegroTransformOffPageConnectorGenerator;

function requireAllegroTransformOffPageConnectorGenerator () {
	if (hasRequiredAllegroTransformOffPageConnectorGenerator) return AllegroTransformOffPageConnectorGenerator;
	hasRequiredAllegroTransformOffPageConnectorGenerator = 1;
	Object.defineProperty(AllegroTransformOffPageConnectorGenerator, "__esModule", { value: true });
	AllegroTransformOffPageConnectorGenerator.AllegroTransformOffPageConnectorGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformNetLabel_1 = requireTransformNetLabel();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	const allegroLayers_1 = requireAllegroLayers();
	const LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Label].id;
	const SYMBOL_LAYER = allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Symbol].id;
	class AllegroTransformOffPageConnectorGenerator$1 {
	    constructor() {
	        this.runsFor = [
	            TransformSchematic_1.TransformSchematic,
	        ];
	    }
	    *generate(schematic, rootContext) {
	        const pageNum = schematic.instance.hierarchyEntry.loaded.pageNum;
	        const design = rootContext.resolved.designs.find((d) => d.pageNum === pageNum);
	        if (design === undefined) {
	            return;
	        }
	        for (const symbolLabel of design.symbolLabels) {
	            yield* emit(schematic, symbolLabel, design.defaultTextStyle);
	        }
	    }
	}
	AllegroTransformOffPageConnectorGenerator.AllegroTransformOffPageConnectorGenerator = AllegroTransformOffPageConnectorGenerator$1;
	function* emit(schematic, symbolLabel, textStyle) {
	    const netLabel = new TransformNetLabel_1.TransformNetLabel(schematic, {
	        position: symbolLabel.combobulatorAnchor,
	        originalLabel: symbolLabel.labelText,
	        labelType: 'system',
	        strategyMetadata: symbolLabel.netName !== null ? { xconNetName: symbolLabel.netName } : undefined,
	    }, labelDrawing(symbolLabel.labelText, symbolLabel.drawPosition, textStyle));
	    yield netLabel;
	    if (symbolLabel.groupDrawings.length > 0) {
	        yield new TransformSchematicDrawing_1.TransformSchematicDrawing(schematic, {
	            layers: [SYMBOL_LAYER],
	            drawing: {
	                type: 'group',
	                pos: symbolLabel.groupOrigin,
	                angle: 0,
	                drawings: symbolLabel.groupDrawings,
	            },
	        }, netLabel);
	    }
	}
	function labelDrawing(text, pos, style) {
	    const attr = [];
	    if (style.bold) {
	        attr.push('bold');
	    }
	    if (style.italic) {
	        attr.push('italic');
	    }
	    return {
	        layers: [LABEL_LAYER],
	        drawing: {
	            type: 'text',
	            value: text,
	            pos,
	            size: style.size,
	            thickness: 1,
	            justify: [-1, -1],
	            ...(attr.length > 0 ? { attr } : {}),
	            ...(style.color !== null ? { colorOverride: style.color } : {}),
	        },
	    };
	}
	
	return AllegroTransformOffPageConnectorGenerator;
}

var AllegroTransformGlobalSymbolGenerator = {};

var hasRequiredAllegroTransformGlobalSymbolGenerator;

function requireAllegroTransformGlobalSymbolGenerator () {
	if (hasRequiredAllegroTransformGlobalSymbolGenerator) return AllegroTransformGlobalSymbolGenerator;
	hasRequiredAllegroTransformGlobalSymbolGenerator = 1;
	Object.defineProperty(AllegroTransformGlobalSymbolGenerator, "__esModule", { value: true });
	AllegroTransformGlobalSymbolGenerator.AllegroTransformGlobalSymbolGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const TransformObject_1 = requireTransformObject();
	const TransformProject_1 = requireTransformProject();
	const TransformProjectModule_1 = requireTransformProjectModule();
	const TransformModule_1 = requireTransformModule();
	const TransformModulePinConnection_1 = requireTransformModulePinConnection();
	const TransformSchematic_1 = requireTransformSchematic();
	const allegroLayers_1 = requireAllegroLayers();
	const AllegroRecordHelpers_1 = requireAllegroRecordHelpers();
	const AllegroResolved_1 = requireAllegroResolved();
	const VALUE_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.ValueLabel].id;
	class AllegroTransformGlobalSymbolGenerator$1 {
	    constructor() {
	        this.runsFor = [
	            TransformSchematic_1.TransformSchematic,
	        ];
	    }
	    *generate(schematic, rootContext) {
	        const project = TransformObject_1.TransformObject.FindParent(schematic, TransformProject_1.TransformProject);
	        if (project === null) {
	            return;
	        }
	        const pageNum = schematic.instance.hierarchyEntry.loaded.pageNum;
	        const design = rootContext.resolved.designs.find((d) => d.pageNum === pageNum);
	        if (design === undefined) {
	            return;
	        }
	        for (const symbol of design.powerSymbols) {
	            yield* emit(schematic, project, rootContext, symbol, design);
	        }
	    }
	}
	AllegroTransformGlobalSymbolGenerator.AllegroTransformGlobalSymbolGenerator = AllegroTransformGlobalSymbolGenerator$1;
	function* emit(schematic, project, rootContext, resolved, design) {
	    const component = rootContext.componentsByCellName.get(resolved.cellName);
	    if (component === undefined || resolved.rawInstance.kind !== 'power') {
	        return;
	    }
	    const symbolNetName = resolved.value ?? null;
	    const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
	        name: resolved.refdes,
	        component,
	        value: symbolNetName ?? undefined,
	        metadata: symbolNetName !== null ? rootContext.netAttributesByName.get(symbolNetName) : undefined,
	        model: buildSigmaModel(resolved, symbolNetName),
	    });
	    yield projectModule;
	    yield new TransformModule_1.TransformModule(schematic, {
	        ref: resolved.moduleRef,
	        footprintRef: (0, AllegroResolved_1.allegroSymbolRefForCell)(resolved.cellName, resolved.view, resolved.sectionNumber),
	        center: resolved.center,
	        transform: resolved.transform,
	        name: resolved.refdes,
	        drawProperties: buildDrawProperties(resolved, symbolNetName ?? resolved.cellName, design.defaultTextStyle),
	    }, component, projectModule);
	    for (const pin of resolved.pins) {
	        yield new TransformModulePinConnection_1.TransformModulePinConnection(schematic, {
	            position: pin.absolutePosition,
	            connection: {
	                moduleRef: resolved.moduleRef,
	                moduleName: resolved.refdes,
	                pinRef: pin.ref,
	                skipPositionalCombobulation: true,
	                appliesNetName: pin.netName !== null ?
	                    { label: pin.netName, labelType: 'system', strategyMetadata: { xconNetName: pin.netName } }
	                    : null,
	            },
	        });
	    }
	}
	function buildSigmaModel(resolved, netName) {
	    if (resolved.signalKind === 'ground') {
	        return { modelId: 'ground.standard' };
	    }
	    const model = { modelId: 'supply.standard' };
	    if (resolved.signalVoltage !== undefined) {
	        model.inferenceReport = {
	            parameters: { voltage: { source: 'parser', value: resolved.signalVoltage } },
	        };
	    }
	    else if (netName !== null) {
	        try {
	            const voltage = (0, sigma_engine_shared_1.parseNumericalSIValue)(netName, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions['voltage']);
	            model.inferenceReport = {
	                parameters: { voltage: { source: 'module-value', value: voltage } },
	            };
	        }
	        catch {
	        }
	    }
	    return model;
	}
	function buildDrawProperties(resolved, netName, textStyle) {
	    if (resolved.rawInstance.kind !== 'power') {
	        return {};
	    }
	    const center = resolved.center;
	    let pos = [center[0], center[1]];
	    for (const label of (0, AllegroRecordHelpers_1.iterateVisualLabels)(resolved.rawInstance.record)) {
	        if (label.propertyName?.toUpperCase() === 'VALUE' && label.position !== null) {
	            pos = [
	                center[0] + label.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	                center[1] + label.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM,
	            ];
	            break;
	        }
	    }
	    return {
	        Value: {
	            value: netName,
	            pos,
	            layer: VALUE_LABEL_LAYER,
	            angle: 0,
	            justify: [-1, -1],
	            size: textStyle.size,
	            thickness: 1,
	            hidden: false,
	            mirror: [false, false],
	        },
	    };
	}
	
	return AllegroTransformGlobalSymbolGenerator;
}

var AllegroTransformGraphicsGenerator = {};

var hasRequiredAllegroTransformGraphicsGenerator;

function requireAllegroTransformGraphicsGenerator () {
	if (hasRequiredAllegroTransformGraphicsGenerator) return AllegroTransformGraphicsGenerator;
	hasRequiredAllegroTransformGraphicsGenerator = 1;
	Object.defineProperty(AllegroTransformGraphicsGenerator, "__esModule", { value: true });
	AllegroTransformGraphicsGenerator.AllegroTransformGraphicsGenerator = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const hash_1 = requireHash();
	const TransformSchematic_1 = requireTransformSchematic();
	const TransformSchematicDrawing_1 = requireTransformSchematicDrawing();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const allegroLayers_1 = requireAllegroLayers();
	class AllegroTransformGraphicsGenerator$1 {
	    constructor() {
	        this.runsFor = [
	            TransformSchematic_1.TransformSchematic,
	        ];
	    }
	    *generate(schematic, rootContext) {
	        const pageNum = schematic.instance.hierarchyEntry.loaded.pageNum;
	        const design = rootContext.resolved.designs.find((d) => d.pageNum === pageNum);
	        if (design === undefined) {
	            return;
	        }
	        yield* emitRichTexts(schematic, design);
	        yield* emitImages(schematic, design, rootContext);
	        yield* emitDashedRects(schematic, design);
	    }
	}
	AllegroTransformGraphicsGenerator.AllegroTransformGraphicsGenerator = AllegroTransformGraphicsGenerator$1;
	function* emitRichTexts(schematic, design) {
	    for (const richText of design.richTexts) {
	        const drawing = {
	            layers: [allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Other].id],
	            drawing: buildRichTextDrawing(richText),
	        };
	        yield new TransformSchematicDrawing_1.TransformSchematicDrawing(schematic, drawing);
	    }
	}
	function buildRichTextDrawing(richText) {
	    return {
	        type: 'rich-text',
	        pos: richText.pos,
	        width: richText.width,
	        height: richText.height,
	        paragraphs: richText.paragraphs,
	        ...(richText.defaultSizePt !== undefined ? { defaultSizePt: richText.defaultSizePt } : {}),
	        ...(richText.defaultBold !== undefined ? { defaultBold: richText.defaultBold } : {}),
	        ...(richText.defaultItalic !== undefined ? { defaultItalic: richText.defaultItalic } : {}),
	        ...(richText.defaultFont !== undefined ? { defaultFont: richText.defaultFont } : {}),
	    };
	}
	function* emitDashedRects(schematic, design) {
	    for (const rect of design.dashedRects) {
	        const drawing = {
	            layers: [allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Other].id],
	            drawing: buildDashedRect(rect),
	        };
	        yield new TransformSchematicDrawing_1.TransformSchematicDrawing(schematic, drawing);
	    }
	}
	const DASHED_RECT_STROKE_WIDTH_MM = 0.2;
	function buildDashedRect(rect) {
	    return {
	        type: 'rect',
	        start: rect.pos,
	        end: [rect.pos[0] + rect.width, rect.pos[1] + rect.height],
	        strokeWidth: DASHED_RECT_STROKE_WIDTH_MM,
	        lineStyle: 'dash',
	    };
	}
	function* emitImages(schematic, design, rootContext) {
	    for (const image of design.images) {
	        const bytes = rootContext.resolveImageBytes(image.filename);
	        if (bytes === null) {
	            continue;
	        }
	        const mime = detectImageMimeType(bytes);
	        if (mime === null) {
	            continue;
	        }
	        const ref = `images/${(0, hash_1.fnv1aHash)(image.filename)}.${mime.ext}`;
	        const drawing = {
	            layers: [allegroLayers_1.AllegroSchLayerMap[sigma_engine_shared_1.DesignModels.SchLayerType.Image].id],
	            drawing: buildImageDrawing(image, ref),
	        };
	        yield new TransformSchematicDrawing_1.TransformSchematicDrawing(schematic, drawing);
	        yield new TransformImageExtraction_1.TransformImageExtraction(schematic, ref, image.filename, bytes, mime.mimeType);
	    }
	}
	function buildImageDrawing(image, ref) {
	    const drawing = {
	        type: 'image',
	        pos: image.pos,
	        width: image.width,
	        height: image.height,
	        imageRef: ref,
	    };
	    if (image.angle !== 0) {
	        drawing.angle = image.angle;
	    }
	    return drawing;
	}
	function detectImageMimeType(data) {
	    if (data.length < 4) {
	        return null;
	    }
	    const isPng = data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47;
	    if (isPng) {
	        return { mimeType: 'image/png', ext: 'png' };
	    }
	    const isJpeg = data[0] === 0xff && data[1] === 0xd8;
	    if (isJpeg) {
	        return { mimeType: 'image/jpeg', ext: 'jpg' };
	    }
	    const isBmp = data[0] === 0x42 && data[1] === 0x4d;
	    if (isBmp) {
	        return { mimeType: 'image/bmp', ext: 'bmp' };
	    }
	    return { mimeType: 'application/octet-stream', ext: 'bin' };
	}
	
	return AllegroTransformGraphicsGenerator;
}

var hasRequiredAllegroOrchestratedTransformer;

function requireAllegroOrchestratedTransformer () {
	if (hasRequiredAllegroOrchestratedTransformer) return AllegroOrchestratedTransformer;
	hasRequiredAllegroOrchestratedTransformer = 1;
	Object.defineProperty(AllegroOrchestratedTransformer, "__esModule", { value: true });
	AllegroOrchestratedTransformer.transformAllegroProject = transformAllegroProject;
	const TransformOrchestrator_1 = requireTransformOrchestrator();
	const TransformProject_1 = requireTransformProject();
	const TransformImageExtraction_1 = requireTransformImageExtraction();
	const AllegroTransformProjectGenerator_1 = requireAllegroTransformProjectGenerator();
	const AllegroTransformSchematicGenerator_1 = requireAllegroTransformSchematicGenerator();
	const AllegroTransformComponentGenerator_1 = requireAllegroTransformComponentGenerator();
	const AllegroTransformModuleGenerator_1 = requireAllegroTransformModuleGenerator();
	const AllegroTransformNetWireGenerator_1 = requireAllegroTransformNetWireGenerator();
	const AllegroTransformOffPageConnectorGenerator_1 = requireAllegroTransformOffPageConnectorGenerator();
	const AllegroTransformGlobalSymbolGenerator_1 = requireAllegroTransformGlobalSymbolGenerator();
	const AllegroTransformGraphicsGenerator_1 = requireAllegroTransformGraphicsGenerator();
	function transformAllegroProject(rootContext) {
	    const transformer = new TransformOrchestrator_1.TransformOrchestrator({
	        generators: [
	            new AllegroTransformProjectGenerator_1.AllegroTransformProjectGenerator(),
	            new AllegroTransformSchematicGenerator_1.AllegroTransformSchematicGenerator(),
	            new AllegroTransformComponentGenerator_1.AllegroTransformComponentGenerator(),
	            new AllegroTransformModuleGenerator_1.AllegroTransformModuleGenerator(),
	            new AllegroTransformNetWireGenerator_1.AllegroTransformNetWireGenerator(),
	            new AllegroTransformOffPageConnectorGenerator_1.AllegroTransformOffPageConnectorGenerator(),
	            new AllegroTransformGlobalSymbolGenerator_1.AllegroTransformGlobalSymbolGenerator(),
	            new AllegroTransformGraphicsGenerator_1.AllegroTransformGraphicsGenerator(),
	        ],
	    });
	    const projectModels = transformer.transform(rootContext, TransformProject_1.TransformProject);
	    const imageExtractionObjects = transformer.getRegistry().getPrimaries(TransformImageExtraction_1.TransformImageExtraction);
	    const extractedImages = imageExtractionObjects.map((obj) => obj.compiled);
	    return { projectModels, extractedImages };
	}
	
	return AllegroOrchestratedTransformer;
}

var sdaxArchive = {};

var hasRequiredSdaxArchive;

function requireSdaxArchive () {
	if (hasRequiredSdaxArchive) return sdaxArchive;
	hasRequiredSdaxArchive = 1;
	Object.defineProperty(sdaxArchive, "__esModule", { value: true });
	sdaxArchive.extractSdax = extractSdax;
	sdaxArchive.readEntryAsText = readEntryAsText;
	sdaxArchive.findEntriesByExtension = findEntriesByExtension;
	const fflate_1 = /*@__PURE__*/ requireBrowser();
	const utf8Decoder = new TextDecoder('utf-8');
	function extractSdax(data) {
	    const unzipped = (0, fflate_1.unzipSync)(data);
	    const entries = new Map();
	    for (const path of Object.keys(unzipped)) {
	        if (path.endsWith('/')) {
	            continue;
	        }
	        entries.set(path, { path, contents: unzipped[path] });
	    }
	    return { entries };
	}
	function readEntryAsText(entry) {
	    return utf8Decoder.decode(entry.contents);
	}
	function findEntriesByExtension(extracted, extension) {
	    const ext = extension.toLowerCase();
	    const out = [];
	    for (const path of extracted.entries.keys()) {
	        if (path.toLowerCase().endsWith(ext)) {
	            out.push(path);
	        }
	    }
	    return out;
	}
	
	return sdaxArchive;
}

var xconParser = {};

var hasRequiredXconParser;

function requireXconParser () {
	if (hasRequiredXconParser) return xconParser;
	hasRequiredXconParser = 1;
	Object.defineProperty(xconParser, "__esModule", { value: true });
	xconParser.parseXcon = parseXcon;
	const xml_js_1 = requireLib$1z();
	function parseXcon(content) {
	    const root = (0, xml_js_1.xml2js)(content, { compact: false, addParent: false });
	    const schemaEl = findChild(root, 'schema');
	    if (schemaEl === null) {
	        throw new Error('xcon: missing <schema> root element');
	    }
	    const headerEl = findChild(schemaEl, 'header');
	    const designsEl = findChild(schemaEl, 'designs');
	    const schemaVersion = textOf(findChild(headerEl, 'schemaVersion'));
	    const creatorTool = textOf(findChild(headerEl, 'creatorTool'));
	    const savedLibrary = textOf(findChild(headerEl, 'savedLibrary'));
	    const designs = (designsEl?.elements ?? [])
	        .filter((el) => el.type === 'element' && el.name === 'design')
	        .map(parseDesign);
	    return { schemaVersion, creatorTool, savedLibrary, designs };
	}
	function parseDesign(designEl) {
	    const cells = new Map();
	    for (const cellEl of childrenNamed(findChild(designEl, 'cells'), 'cell')) {
	        const cell = parseCell(cellEl);
	        cells.set(cell.id, cell);
	    }
	    const nets = new Map();
	    for (const netEl of childrenNamed(findChild(designEl, 'nets'), 'net')) {
	        const net = parseNet(netEl);
	        nets.set(net.id, net);
	    }
	    const instances = new Map();
	    for (const instEl of childrenNamed(findChild(designEl, 'instances'), 'instance')) {
	        const instance = parseInstance(instEl);
	        instances.set(instance.id, instance);
	    }
	    return {
	        name: attrOf(designEl, 'name'),
	        view: attrOf(designEl, 'view'),
	        cells,
	        nets,
	        instances,
	    };
	}
	function parseCell(cellEl) {
	    const terms = childrenNamed(findChild(cellEl, 'terms'), 'term').map((termEl) => ({
	        id: textOf(findChild(termEl, 'id')),
	        name: textOf(findChild(termEl, 'name')),
	        direction: textOf(findChild(termEl, 'direction')),
	    }));
	    return {
	        id: textOf(findChild(cellEl, 'id')),
	        library: textOf(findChild(cellEl, 'library')),
	        name: textOf(findChild(cellEl, 'name')),
	        view: textOf(findChild(cellEl, 'view')),
	        terms,
	    };
	}
	function parseNet(netEl) {
	    return {
	        id: textOf(findChild(netEl, 'id')),
	        name: textOf(findChild(netEl, 'name')),
	        scope: textOf(findChild(netEl, 'scope')),
	        direction: textOf(findChild(netEl, 'direction')),
	    };
	}
	function parseInstance(instEl) {
	    const parameters = childrenNamed(findChild(instEl, 'parameters'), 'parameter').map((paramEl) => ({
	        name: textOf(findChild(paramEl, 'name')),
	        value: textOf(findChild(paramEl, 'value')),
	    }));
	    const pins = childrenNamed(findChild(instEl, 'pins'), 'pin').map(parsePin);
	    return {
	        id: textOf(findChild(instEl, 'id')),
	        cellId: textOf(findChild(instEl, 'cellid')),
	        name: textOf(findChild(instEl, 'name')),
	        parameters,
	        pins,
	    };
	}
	function parsePin(pinEl) {
	    const netIds = [];
	    for (const conn of childrenNamed(findChild(pinEl, 'connections'), 'connection')) {
	        const netAttr = attrOf(conn, 'net');
	        if (netAttr.length > 0) {
	            netIds.push(netAttr);
	        }
	    }
	    return {
	        id: textOf(findChild(pinEl, 'id')),
	        termId: textOf(findChild(pinEl, 'termid')),
	        netIds,
	    };
	}
	function findChild(parent, name) {
	    if (parent === null || parent.elements === undefined) {
	        return null;
	    }
	    for (const el of parent.elements) {
	        if (el.type === 'element' && el.name === name) {
	            return el;
	        }
	    }
	    return null;
	}
	function childrenNamed(parent, name) {
	    if (parent === null || parent.elements === undefined) {
	        return [];
	    }
	    return parent.elements.filter((el) => el.type === 'element' && el.name === name);
	}
	function textOf(el) {
	    if (el === null || el.elements === undefined) {
	        return '';
	    }
	    let out = '';
	    for (const child of el.elements) {
	        if (child.type === 'text' && typeof child.text === 'string') {
	            out += child.text;
	        }
	        else if (child.type === 'cdata' && typeof child.cdata === 'string') {
	            out += child.cdata;
	        }
	    }
	    return out;
	}
	function attrOf(el, name) {
	    const attrs = el.attributes;
	    if (attrs === undefined) {
	        return '';
	    }
	    const v = attrs[name];
	    return typeof v === 'string' ? v : '';
	}
	
	return xconParser;
}

var dxJsonParser = {};

var hasRequiredDxJsonParser;

function requireDxJsonParser () {
	if (hasRequiredDxJsonParser) return dxJsonParser;
	hasRequiredDxJsonParser = 1;
	Object.defineProperty(dxJsonParser, "__esModule", { value: true });
	dxJsonParser.parseDx = parseDx;
	const CPATH_INSTANCE_ID = /\\I(\d+)\\/;
	function parseDx(text) {
	    const raw = JSON.parse(text);
	    const rawInstances = Array.isArray(raw['instances']) ? raw['instances'] : [];
	    const instances = [];
	    const instancesByXconId = new Map();
	    for (const rawInst of rawInstances) {
	        if (typeof rawInst !== 'object' || rawInst === null) {
	            continue;
	        }
	        const inst = rawInst;
	        const cpath = typeof inst['cpath'] === 'string' ? inst['cpath'] : '';
	        const xconInstanceId = extractXconInstanceId(cpath);
	        const rawProps = inst['properties'];
	        const properties = typeof rawProps === 'object' && rawProps !== null ? normalizeStringMap(rawProps) : {};
	        const rawAttrs = inst['attributes'];
	        const attrsMap = typeof rawAttrs === 'object' && rawAttrs !== null ? normalizeStringMap(rawAttrs) : {};
	        const attributes = {
	            refdes: attrsMap['refdes'] ?? '',
	            symbol: attrsMap['symbol'] ?? '',
	            HASHID: attrsMap['HASHID'] ?? '',
	            library: attrsMap['library'] ?? '',
	            system_capture_model: attrsMap['system_capture_model'] ?? '',
	            partid: attrsMap['partid'] ?? '',
	        };
	        const dxInstance = { cpath, xconInstanceId, properties, attributes };
	        instances.push(dxInstance);
	        if (xconInstanceId.length > 0) {
	            instancesByXconId.set(xconInstanceId, dxInstance);
	        }
	    }
	    return { instances, instancesByXconId };
	}
	function extractXconInstanceId(cpath) {
	    const match = CPATH_INSTANCE_ID.exec(cpath);
	    return match !== null ? `I${match[1]}` : '';
	}
	function normalizeStringMap(obj) {
	    const out = {};
	    for (const [k, v] of Object.entries(obj)) {
	        if (typeof v === 'string') {
	            out[k] = v;
	        }
	        else if (typeof v === 'number' || typeof v === 'boolean') {
	            out[k] = String(v);
	        }
	    }
	    return out;
	}
	
	return dxJsonParser;
}

var parser = {};

var hasRequiredParser;

function requireParser () {
	if (hasRequiredParser) return parser;
	hasRequiredParser = 1;
	Object.defineProperty(parser, "__esModule", { value: true });
	parser.BracketParser = void 0;
	const TYPE32_HTML_HEADER = /(\d+) (\d+) (\d+) /y;
	class BracketParser {
	    constructor(data) {
	        this.data = data;
	        this.cursor = 0;
	    }
	    parse() {
	        this.cursor = 0;
	        return { nodes: this.parseLevel(null) };
	    }
	    parseLevel(closeKind) {
	        const nodes = [];
	        while (!this.isEof()) {
	            this.skipWhitespace();
	            if (this.isEof()) {
	                break;
	            }
	            if (closeKind !== null) {
	                if (this.peek(2) === '/>') {
	                    this.cursor += 2;
	                    this.consumeSpaces(2);
	                    break;
	                }
	                if (closeKind === 'econtainer' && this.peek(3) === 'e/>') {
	                    this.cursor += 3;
	                    this.consumeSpaces(2);
	                    break;
	                }
	            }
	            if (this.peek(1) === '<') {
	                const node = this.parseBracket();
	                nodes.push(node);
	                if (node.type === 'atom' && node.content === 'zeronull') {
	                    this.parseZeronullFollowOn(nodes);
	                    continue;
	                }
	                if (node.type === 'atom' && node.content === '9' && !this.isEof() && /\d/.test(this.data[this.cursor])) {
	                    nodes.push(this.parseType32Blob());
	                    continue;
	                }
	            }
	            else {
	                const start = this.cursor;
	                while (!this.isEof()) {
	                    if (this.peek(1) === '<') {
	                        break;
	                    }
	                    if (closeKind !== null && this.peek(2) === '/>') {
	                        break;
	                    }
	                    if (closeKind === 'econtainer' && this.peek(3) === 'e/>') {
	                        break;
	                    }
	                    this.cursor++;
	                }
	                const text = this.data.slice(start, this.cursor).trim();
	                if (text.length > 0) {
	                    nodes.push({ type: 'text', content: text });
	                }
	            }
	        }
	        return nodes;
	    }
	    parseBracket() {
	        this.cursor++;
	        const next2 = this.peek(2);
	        if (next2 === '  ') {
	            this.cursor += 2;
	            const children = this.parseLevel('container');
	            return { type: 'container', children };
	        }
	        if (next2 === 'e ') {
	            this.cursor += 2;
	            const children = this.parseLevel('econtainer');
	            return { type: 'econtainer', children };
	        }
	        if (next2 === 'n ') {
	            this.cursor += 2;
	            const content = this.findAndConsume(' n/>');
	            this.consumeSpaces(2);
	            return { type: 'name', content };
	        }
	        if (next2 === 'v ') {
	            this.cursor += 2;
	            const content = this.findAndConsume(' v/>');
	            this.consumeSpaces(3);
	            return { type: 'value', content };
	        }
	        const content = this.findAndConsume('/>').trim();
	        this.consumeSpaces(2);
	        return { type: 'atom', content };
	    }
	    parseZeronullFollowOn(nodes) {
	        this.skipWhitespace();
	        if (!this.isEof() && this.peek(1) === '<') {
	            nodes.push(this.parseBracket());
	        }
	        this.skipWhitespace();
	        if (this.isEof() || this.peek(1) !== '<') {
	            return;
	        }
	        const lengthNode = this.parseBracket();
	        nodes.push(lengthNode);
	        if (lengthNode.type !== 'atom') {
	            return;
	        }
	        const byteLen = parseInt(lengthNode.content, 10);
	        if (isNaN(byteLen) || byteLen <= 0) {
	            nodes.push({ type: 'atom', content: '' });
	            return;
	        }
	        this.skipWhitespace();
	        nodes.push(this.parseAtomByCount(byteLen));
	    }
	    parseAtomByCount(byteLen) {
	        if (this.peek(1) === '<') {
	            this.cursor++;
	        }
	        if (this.peek(1) === ' ') {
	            this.cursor++;
	        }
	        const endChar = this.charOffsetForUtf8Bytes(this.cursor, byteLen);
	        const content = this.data.slice(this.cursor, endChar);
	        this.cursor = endChar;
	        if (this.peek(1) === ' ') {
	            this.cursor++;
	        }
	        if (this.peek(2) === '/>') {
	            this.cursor += 2;
	        }
	        this.consumeSpaces(2);
	        return { type: 'atom', content };
	    }
	    parseType32Blob() {
	        TYPE32_HTML_HEADER.lastIndex = this.cursor;
	        const match = TYPE32_HTML_HEADER.exec(this.data);
	        if (match === null) {
	            return { type: 'raw-blob', x: 0, y: 0, content: '' };
	        }
	        const x = parseInt(match[1], 10);
	        const y = parseInt(match[2], 10);
	        const byteLen = parseInt(match[3], 10);
	        this.cursor = TYPE32_HTML_HEADER.lastIndex;
	        const content = this.consumeByCount(byteLen);
	        this.consumeSpaces(2);
	        return { type: 'raw-blob', x, y, content };
	    }
	    consumeByCount(byteLen) {
	        const endChar = this.charOffsetForUtf8Bytes(this.cursor, byteLen);
	        const content = this.data.slice(this.cursor, endChar);
	        this.cursor = endChar;
	        return content;
	    }
	    charOffsetForUtf8Bytes(startChar, byteLen) {
	        let bytesConsumed = 0;
	        let i = startChar;
	        while (i < this.data.length) {
	            const code = this.data.codePointAt(i) ?? 0;
	            const charBytes = code <= 0x7f ? 1
	                : code <= 0x7ff ? 2
	                    : code <= 0xffff ? 3
	                        : 4;
	            if (bytesConsumed + charBytes > byteLen) {
	                break;
	            }
	            bytesConsumed += charBytes;
	            i += code > 0xffff ? 2 : 1;
	        }
	        return i;
	    }
	    isEof() {
	        return this.cursor >= this.data.length;
	    }
	    peek(width) {
	        return this.data.slice(this.cursor, this.cursor + width);
	    }
	    skipWhitespace() {
	        while (!this.isEof() &&
	            (this.data[this.cursor] === ' ' ||
	                this.data[this.cursor] === '\n' ||
	                this.data[this.cursor] === '\r' ||
	                this.data[this.cursor] === '\t')) {
	            this.cursor++;
	        }
	    }
	    consumeSpaces(max) {
	        let consumed = 0;
	        while (consumed < max && !this.isEof() && this.data[this.cursor] === ' ') {
	            this.cursor++;
	            consumed++;
	        }
	    }
	    findAndConsume(sentinel) {
	        const idx = this.data.indexOf(sentinel, this.cursor);
	        if (idx < 0) {
	            const remainder = this.data.slice(this.cursor);
	            this.cursor = this.data.length;
	            return remainder;
	        }
	        const slice = this.data.slice(this.cursor, idx);
	        this.cursor = idx + sentinel.length;
	        return slice;
	    }
	}
	parser.BracketParser = BracketParser;
	
	return parser;
}

var decodeDocument = {};

var decodePageDocument = {};

var decodeTc5 = {};

var decodeTc8WireBundle = {};

var decodeTc11WireSegment = {};

var hasRequiredDecodeTc11WireSegment;

function requireDecodeTc11WireSegment () {
	if (hasRequiredDecodeTc11WireSegment) return decodeTc11WireSegment;
	hasRequiredDecodeTc11WireSegment = 1;
	Object.defineProperty(decodeTc11WireSegment, "__esModule", { value: true });
	decodeTc11WireSegment.decodeWireSegment = decodeWireSegment;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
	function decodeWireSegment(node, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:11 (WireSegment), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const c = node.children;
	    if (c.length !== 15) {
	        return node;
	    }
	    const startCoord = (0, decode_tc31_visual_label_1.readCadencePosition)(c[3]);
	    const endCoord = (0, decode_tc31_visual_label_1.readCadencePosition)(c[5]);
	    const bboxQuad = (0, decode_tc31_visual_label_1.readCoordsContainer)(c[7]);
	    if (startCoord === null || endCoord === null) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `tc:11 (WireSegment) missing start (${startCoord === null}) or end (${endCoord === null}) coord container`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	    }
	    return {
	        kind: 'wire-segment',
	        startCoord,
	        endCoord,
	        bbox: bboxQuad,
	        raw: node,
	    };
	}
	
	return decodeTc11WireSegment;
}

var hasRequiredDecodeTc8WireBundle;

function requireDecodeTc8WireBundle () {
	if (hasRequiredDecodeTc8WireBundle) return decodeTc8WireBundle;
	hasRequiredDecodeTc8WireBundle = 1;
	Object.defineProperty(decodeTc8WireBundle, "__esModule", { value: true });
	decodeTc8WireBundle.decodeWireBundle = decodeWireBundle;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const entity_list_1 = requireEntityList();
	const decode_prop_block_1 = requireDecodePropBlock();
	const decode_tc11_wire_segment_1 = requireDecodeTc11WireSegment();
	const typeCodes_1 = requireTypeCodes();
	function decodeWireBundle(node, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:8 (WireBundle), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const c = node.children;
	    if (c.length !== 7) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `tc:8 (WireBundle) has ${c.length} children, expected 7`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const detected = c[2].type === 'container' || c[2].type === 'econtainer' ? (0, entity_list_1.detectEntityList)(c[2].children) : null;
	    const segments = (0, entity_list_1.decodeEntityList)(c[2], null, `${path}/segments`, warnings, (item, _i, itemPath) => (0, decode_tc11_wire_segment_1.decodeWireSegment)(item, itemPath, warnings)) ?? c[2];
	    const props = (0, decode_prop_block_1.decodePropBlock)(c[6], `${path}/props`, warnings);
	    const isBus = detected !== null && detected.typeCode === typeCodes_1.TypeCode.BusSegment;
	    const rowAtom = c[5];
	    const styleRowIndex = rowAtom.type === 'atom' && /^\d+$/.test(rowAtom.content.trim()) ? rowAtom.content.trim() : null;
	    return {
	        kind: 'wire-bundle',
	        segments,
	        props,
	        isBus,
	        styleRowIndex,
	        raw: node,
	    };
	}
	
	return decodeTc8WireBundle;
}

var hasRequiredDecodeTc5;

function requireDecodeTc5 () {
	if (hasRequiredDecodeTc5) return decodeTc5;
	hasRequiredDecodeTc5 = 1;
	Object.defineProperty(decodeTc5, "__esModule", { value: true });
	decodeTc5.decodeNetStub = decodeNetStub;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const decode_prop_block_1 = requireDecodePropBlock();
	const entity_list_1 = requireEntityList();
	const decode_tc8_wire_bundle_1 = requireDecodeTc8WireBundle();
	const typeCodes_1 = requireTypeCodes();
	function decodeNetStub(node, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:5 (NetStub), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const c = node.children;
	    if (c.length !== 7) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `tc:5 (NetStub) has ${c.length} children, expected 7`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const stubIdAtom = c[2];
	    const stubId = stubIdAtom.type === 'atom' ? stubIdAtom.content.trim() : null;
	    const props = (0, decode_prop_block_1.decodePropBlock)(c[6], `${path}/props`, warnings);
	    const isDefault = (0, decode_prop_block_1.findPropValue)(props, 'name') === 'default';
	    const wireBundles = (0, entity_list_1.isPlaceholderContainer)(c[0]) ? null : (((0, entity_list_1.decodeEntityList)(c[0], typeCodes_1.TypeCode.WireBundle, `${path}/wireBundles`, warnings, (item, _i, itemPath) => (0, decode_tc8_wire_bundle_1.decodeWireBundle)(item, itemPath, warnings)) ?? c[0]));
	    return {
	        kind: 'net-stub',
	        stubId,
	        wireBundles,
	        props,
	        isDefault,
	        raw: node,
	    };
	}
	
	return decodeTc5;
}

var decodeTc6Tc14Tc55 = {};

var hasRequiredDecodeTc6Tc14Tc55;

function requireDecodeTc6Tc14Tc55 () {
	if (hasRequiredDecodeTc6Tc14Tc55) return decodeTc6Tc14Tc55;
	hasRequiredDecodeTc6Tc14Tc55 = 1;
	Object.defineProperty(decodeTc6Tc14Tc55, "__esModule", { value: true });
	decodeTc6Tc14Tc55.decodePageBorder = decodePageBorder;
	decodeTc6Tc14Tc55.decodePageMetadata = decodePageMetadata;
	decodeTc6Tc14Tc55.decodePageInstance = decodePageInstance;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const helpers_1 = requireHelpers();
	const decode_prop_block_1 = requireDecodePropBlock();
	const decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
	function decodePageBorder(node, path, warnings) {
	    if (!isContainer(node)) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:6 (PageBorder), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const libId = (0, helpers_1.findPrefixedDeep)(node, '14');
	    const bbox = findFirstCoordsContainer(node);
	    const props = findAndDecodeLastPropBlock(node, `${path}/props`, warnings);
	    return { kind: 'page-border', libId, bbox, props, raw: node };
	}
	function decodePageMetadata(node, path, warnings) {
	    if (!isContainer(node)) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:14 (PageMetadata), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const libId = (0, helpers_1.findPrefixedDeep)(node, '14');
	    const props = findAndDecodeLastPropBlock(node, `${path}/props`, warnings);
	    return { kind: 'page-metadata', libId, props, raw: node };
	}
	function decodePageInstance(node, path, warnings) {
	    if (!isContainer(node)) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:55 (PageInstance), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const c = node.children;
	    const hash = c.length > 1 && c[1].type === 'atom' ? c[1].content.trim() : null;
	    const libId = (0, helpers_1.findPrefixedDeep)(node, '14');
	    const props = findAndDecodeLastPropBlock(node, `${path}/props`, warnings);
	    return { kind: 'page-instance', hash, libId, props, raw: node };
	}
	function isContainer(node) {
	    return node.type === 'container' || node.type === 'econtainer';
	}
	function findFirstCoordsContainer(node) {
	    for (const child of node.children) {
	        const bbox = (0, decode_tc31_visual_label_1.readCoordsContainer)(child);
	        if (bbox !== null) {
	            return bbox;
	        }
	    }
	    return null;
	}
	function findAndDecodeLastPropBlock(node, path, warnings) {
	    for (let i = node.children.length - 1; i >= 0; i--) {
	        const child = node.children[i];
	        const block = (0, decode_prop_block_1.decodePropBlock)(child, path, []);
	        if (block !== null) {
	            return (0, decode_prop_block_1.decodePropBlock)(child, path, warnings);
	        }
	    }
	    return null;
	}
	
	return decodeTc6Tc14Tc55;
}

var decodeGraphicsAnnotation = {};

var hasRequiredDecodeGraphicsAnnotation;

function requireDecodeGraphicsAnnotation () {
	if (hasRequiredDecodeGraphicsAnnotation) return decodeGraphicsAnnotation;
	hasRequiredDecodeGraphicsAnnotation = 1;
	Object.defineProperty(decodeGraphicsAnnotation, "__esModule", { value: true });
	decodeGraphicsAnnotation.decodeGraphicsAnnotation = decodeGraphicsAnnotation$1;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const typeCodes_1 = requireTypeCodes();
	const decode_prop_block_1 = requireDecodePropBlock();
	const decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
	function decodeGraphicsAnnotation$1(node, typeCode, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:${typeCode}, got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const props = findAndDecodeLastPropBlock(node, `${path}/props`, warnings);
	    const subShape = classifySubShape(typeCode, node, props);
	    const coordsContainers = [];
	    for (const child of node.children) {
	        const bbox = (0, decode_tc31_visual_label_1.readCoordsContainer)(child);
	        if (bbox !== null) {
	            coordsContainers.push({ node: child, bbox });
	        }
	    }
	    const bbox = coordsContainers[0]?.bbox ?? null;
	    const position = coordsContainers[1] === undefined ? null : (0, decode_tc31_visual_label_1.readCadencePosition)(coordsContainers[1].node);
	    return {
	        kind: 'graphics-annotation',
	        typeCode,
	        subShape,
	        bbox,
	        position,
	        props,
	        raw: node,
	    };
	}
	function classifySubShape(typeCode, node, props) {
	    const childCount = node.children.length;
	    const hasProp = (name) => (0, decode_prop_block_1.findProp)(props, name) !== null;
	    if (typeCode === typeCodes_1.TypeCode.GraphicsImage) {
	        return 'image-annotation';
	    }
	    if (typeCode === typeCodes_1.TypeCode.GraphicsHtml) {
	        if (hasProp('GRAPHICS_IMAGE_FILE')) {
	            return 'image-annotation';
	        }
	        if (hasProp('pageTableType')) {
	            return 'toc-table-widget';
	        }
	        if (hasProp('CGTYPE')) {
	            return 'standard-html-annotation';
	        }
	    }
	    if (typeCode === typeCodes_1.TypeCode.GraphicsLineArrow) {
	        if (hasProp('GRAPHICS_LINE_ARROW_TYPE')) {
	            return 'line-arrow';
	        }
	        if (hasProp('CGTYPE') && childCount === 20) {
	            return 'standard-html-annotation';
	        }
	        if (hasProp('CGTYPE')) {
	            return 'generic-graphic-with-cgtype';
	        }
	    }
	    if (typeCode === typeCodes_1.TypeCode.GraphicsCallout) {
	        if (hasProp('CGTYPE') && childCount === 20) {
	            return 'standard-html-annotation';
	        }
	        if (hasProp('CGTYPE')) {
	            return 'generic-graphic-with-cgtype';
	        }
	        if (childCount === 10) {
	            return 'generic-graphic-no-cgtype';
	        }
	    }
	    return 'unrecognized';
	}
	function findAndDecodeLastPropBlock(node, path, warnings) {
	    for (let i = node.children.length - 1; i >= 0; i--) {
	        const child = node.children[i];
	        const block = (0, decode_prop_block_1.decodePropBlock)(child, path, []);
	        if (block !== null) {
	            return (0, decode_prop_block_1.decodePropBlock)(child, path, warnings);
	        }
	    }
	    return null;
	}
	
	return decodeGraphicsAnnotation;
}

var decodeTc7Tc12 = {};

var decodeTc13Terminal = {};

var hasRequiredDecodeTc13Terminal;

function requireDecodeTc13Terminal () {
	if (hasRequiredDecodeTc13Terminal) return decodeTc13Terminal;
	hasRequiredDecodeTc13Terminal = 1;
	Object.defineProperty(decodeTc13Terminal, "__esModule", { value: true });
	decodeTc13Terminal.decodeTerminal = decodeTerminal;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const decode_prop_block_1 = requireDecodePropBlock();
	function decodeTerminal(node, path, warnings) {
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for terminal, got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const c = node.children;
	    if (c.length !== 15 && c.length !== 16) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `terminal has ${c.length} children, expected 15 or 16`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const subReference = readPrefixedAtom(c[3], '19:');
	    const positionContainer = isContainer(c[7]) ? c[7] : null;
	    const netHash = readPrefixedAtom(c[9], '5:');
	    const props = (0, decode_prop_block_1.decodePropBlock)(c[14], `${path}/props`, warnings);
	    return {
	        kind: 'terminal',
	        subReference,
	        positionContainer,
	        netHash,
	        props,
	        raw: node,
	    };
	}
	function isContainer(node) {
	    return node.type === 'container' || node.type === 'econtainer';
	}
	function readPrefixedAtom(node, prefix) {
	    if (node.type !== 'atom') {
	        return null;
	    }
	    const content = node.content.trim();
	    if (!content.startsWith(prefix)) {
	        return null;
	    }
	    return content.slice(prefix.length);
	}
	
	return decodeTc13Terminal;
}

var hasRequiredDecodeTc7Tc12;

function requireDecodeTc7Tc12 () {
	if (hasRequiredDecodeTc7Tc12) return decodeTc7Tc12;
	hasRequiredDecodeTc7Tc12 = 1;
	Object.defineProperty(decodeTc7Tc12, "__esModule", { value: true });
	decodeTc7Tc12.decodePowerSymbol = decodePowerSymbol;
	decodeTc7Tc12.decodeComponentInstance = decodeComponentInstance;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const helpers_1 = requireHelpers();
	const entity_list_1 = requireEntityList();
	const typeCodes_1 = requireTypeCodes();
	const decode_prop_block_1 = requireDecodePropBlock();
	const decode_tc13_terminal_1 = requireDecodeTc13Terminal();
	const decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
	function decodePowerSymbol(node, path, warnings) {
	    if (!isContainer(node)) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:7 (PowerSymbol), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    if (node.children.length !== 19) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `tc:7 (PowerSymbol) has ${node.children.length} children, expected 19`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const common = decodeInstanceCommon(node, path, warnings);
	    const associatedNetStubId = (0, decode_prop_block_1.findPropValue)(common.props, 'CDS_ASSOC_NET_ID_STR');
	    return {
	        kind: 'power-symbol',
	        flavor: 'tc7-power-symbol',
	        associatedNetStubId,
	        ...common,
	        raw: node,
	    };
	}
	function decodeComponentInstance(node, path, warnings, instanceHashMap, pageNum) {
	    if (!isContainer(node)) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for tc:12 (ComponentInstance), got ${node.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    if (node.children.length !== 19) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `tc:12 (ComponentInstance) has ${node.children.length} children, expected 19`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const common = decodeInstanceCommon(node, path, warnings);
	    let resolvedInstanceId = null;
	    if (common.placementHash !== null && instanceHashMap !== null && pageNum !== null) {
	        const found = instanceHashMap.get(`${pageNum}:${common.placementHash}`);
	        resolvedInstanceId = found === undefined ? null : found;
	        if (resolvedInstanceId === null) {
	            warnings.push({
	                type: decoded_types_1.DecodeWarningType.CrossRefMiss,
	                message: `tc:12 (ComponentInstance) placement (page=${pageNum}, hash=${common.placementHash}) not found in master.instanceHashMap`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                path: `${path}/placementHash`,
	            });
	        }
	    }
	    return {
	        kind: 'component-instance',
	        flavor: 'tc12-component',
	        resolvedInstanceId,
	        rotation: (0, decode_prop_block_1.findPropValue)(common.props, 'rotation'),
	        transform: (0, decode_prop_block_1.findPropValue)(common.props, 'transform'),
	        zValue: (0, decode_prop_block_1.findPropValue)(common.props, 'zValue'),
	        ...common,
	        raw: node,
	    };
	}
	function decodeInstanceCommon(node, path, warnings) {
	    const c = node.children;
	    const libId = (0, helpers_1.findPrefixed)(c, '14');
	    const modelRef = (0, helpers_1.findPrefixed)(c, '15');
	    const bbox = (0, decode_tc31_visual_label_1.readCoordsContainer)(c[6]);
	    const placementHashAtom = c[10];
	    const placementHash = placementHashAtom.type === 'atom' ? placementHashAtom.content.trim() : null;
	    const placementHashPrefix = placementHash === null ? null : placementHash.slice(0, 3);
	    const position = (0, decode_tc31_visual_label_1.readCadencePosition)(c[12]);
	    const preamble = [
	        c[13]?.type === 'atom' ? c[13].content.trim() : null,
	        c[14]?.type === 'atom' ? c[14].content.trim() : null,
	        c[15]?.type === 'atom' ? c[15].content.trim() : null,
	    ];
	    const styleRowIndex = preamble[2] !== null && /^\d+$/.test(preamble[2]) ? preamble[2] : null;
	    const terminals = buildTerminalsList(c[4], `${path}/terminals`, warnings);
	    const visualLabels = buildVisualLabelsList(c[17], `${path}/visualLabels`, warnings);
	    const props = (0, decode_prop_block_1.decodePropBlock)(c[18], `${path}/props`, warnings);
	    return {
	        libId,
	        modelRef,
	        bbox,
	        position,
	        placementHash,
	        placementHashPrefix,
	        preamble,
	        styleRowIndex,
	        terminals,
	        visualLabels,
	        props,
	    };
	}
	function buildTerminalsList(node, path, warnings) {
	    return (0, entity_list_1.decodeEntityList)(node, typeCodes_1.TypeCode.Terminal, path, warnings, (item, _i, itemPath) => (0, decode_tc13_terminal_1.decodeTerminal)(item, itemPath, warnings));
	}
	function buildVisualLabelsList(node, path, warnings) {
	    return (0, entity_list_1.decodeEntityList)(node, typeCodes_1.TypeCode.VisualLabel, path, warnings, (item, _i, itemPath) => (0, decode_tc31_visual_label_1.decodeVisualLabel)(item, itemPath, warnings));
	}
	function isContainer(node) {
	    void helpers_1.findPrefixedDeep;
	    return node.type === 'container' || node.type === 'econtainer';
	}
	
	return decodeTc7Tc12;
}

var hasRequiredDecodePageDocument;

function requireDecodePageDocument () {
	if (hasRequiredDecodePageDocument) return decodePageDocument;
	hasRequiredDecodePageDocument = 1;
	Object.defineProperty(decodePageDocument, "__esModule", { value: true });
	decodePageDocument.decodePageDocument = decodePageDocument$1;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const entity_list_1 = requireEntityList();
	const typeCodes_1 = requireTypeCodes();
	const decode_prop_block_1 = requireDecodePropBlock();
	const decode_tc5_1 = requireDecodeTc5();
	const decode_tc6_tc14_tc55_1 = requireDecodeTc6Tc14Tc55();
	const decode_graphics_annotation_1 = requireDecodeGraphicsAnnotation();
	const decode_tc7_tc12_1 = requireDecodeTc7Tc12();
	var SLOT;
	(function (SLOT) {
	    SLOT[SLOT["pageInstance"] = 0] = "pageInstance";
	    SLOT[SLOT["headContainer1"] = 1] = "headContainer1";
	    SLOT[SLOT["headContainer2"] = 2] = "headContainer2";
	    SLOT[SLOT["headSeparator"] = 3] = "headSeparator";
	    SLOT[SLOT["metadataStart"] = 4] = "metadataStart";
	    SLOT[SLOT["metadataEnd"] = 9] = "metadataEnd";
	    SLOT[SLOT["pageMetadata"] = 10] = "pageMetadata";
	    SLOT[SLOT["netStubs"] = 11] = "netStubs";
	    SLOT[SLOT["pageBorder"] = 12] = "pageBorder";
	    SLOT[SLOT["powerSymbols"] = 13] = "powerSymbols";
	    SLOT[SLOT["componentInstances"] = 14] = "componentInstances";
	    SLOT[SLOT["graphics"] = 15] = "graphics";
	    SLOT[SLOT["tailFlag"] = 16] = "tailFlag";
	    SLOT[SLOT["tailMarker"] = 17] = "tailMarker";
	    SLOT[SLOT["tailHashMarker"] = 18] = "tailHashMarker";
	    SLOT[SLOT["pageProperties"] = 19] = "pageProperties";
	})(SLOT || (SLOT = {}));
	const GRAPHICS_TYPE_CODES = new Set([
	    typeCodes_1.TypeCode.GraphicsLineArrow,
	    typeCodes_1.TypeCode.GraphicsCallout,
	    typeCodes_1.TypeCode.GraphicsHtml,
	    typeCodes_1.TypeCode.GraphicsImage,
	]);
	function decodePageDocument$1(root, path, warnings, instanceHashMap, pageNum) {
	    const c = root.children;
	    if (c.length !== 20) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `page-root has ${c.length} children, expected 20`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	    }
	    const pageInstance = decodeSingletonEntityList(c[SLOT.pageInstance], typeCodes_1.TypeCode.PageInstance, `${path}/pageInstance`, warnings, (item, p) => (0, decode_tc6_tc14_tc55_1.decodePageInstance)(item, p, warnings));
	    const pageMetadata = decodeSingletonEntityList(c[SLOT.pageMetadata], typeCodes_1.TypeCode.PageMetadata, `${path}/pageMetadata`, warnings, (item, p) => (0, decode_tc6_tc14_tc55_1.decodePageMetadata)(item, p, warnings));
	    const pageBorder = decodeSingletonEntityList(c[SLOT.pageBorder], typeCodes_1.TypeCode.PageBorder, `${path}/pageBorder`, warnings, (item, p) => (0, decode_tc6_tc14_tc55_1.decodePageBorder)(item, p, warnings));
	    const netStubs = decodeOrRaw(c[SLOT.netStubs], `${path}/netStubs`, warnings, (n, p, w) => (0, entity_list_1.decodeEntityList)(n, typeCodes_1.TypeCode.NetStub, p, w, (item, _i, itemPath) => (0, decode_tc5_1.decodeNetStub)(item, itemPath, w)));
	    const powerSymbols = decodeConditionalSlot(c[SLOT.powerSymbols], `${path}/powerSymbols`, warnings, (n, p, w) => (0, entity_list_1.decodeEntityList)(n, typeCodes_1.TypeCode.PowerSymbol, p, w, (item, _i, itemPath) => (0, decode_tc7_tc12_1.decodePowerSymbol)(item, itemPath, w)));
	    const componentInstances = decodeConditionalSlot(c[SLOT.componentInstances], `${path}/componentInstances`, warnings, (n, p, w) => (0, entity_list_1.decodeEntityList)(n, typeCodes_1.TypeCode.ComponentInstance, p, w, (item, _i, itemPath) => (0, decode_tc7_tc12_1.decodeComponentInstance)(item, itemPath, w, instanceHashMap, pageNum)));
	    const graphics = decodeGraphicsSlot(c[SLOT.graphics], `${path}/graphics`, warnings);
	    const pagePropertiesNode = c[SLOT.pageProperties];
	    const pageProperties = pagePropertiesNode === undefined ? null : (0, decode_prop_block_1.decodePropBlock)(pagePropertiesNode, `${path}/pageProperties`, warnings);
	    const metadataAtoms = c.slice(SLOT.metadataStart, SLOT.metadataEnd + 1);
	    const pageNameAtom = metadataAtoms.find((n) => n.type === 'atom' && /[A-Za-z]/.test(n.content));
	    const pageName = pageNameAtom === undefined || pageNameAtom.type !== 'atom' ? null : pageNameAtom.content.trim();
	    const slots = {
	        pageInstance: pageInstance ?? c[SLOT.pageInstance],
	        headContainer1: c[SLOT.headContainer1],
	        headContainer2: c[SLOT.headContainer2],
	        headSeparator: c[SLOT.headSeparator],
	        metadataAtoms,
	        pageName,
	        pageMetadata: pageMetadata ?? c[SLOT.pageMetadata],
	        netStubs: netStubs ?? c[SLOT.netStubs],
	        pageBorder: pageBorder ?? c[SLOT.pageBorder],
	        powerSymbols,
	        componentInstances,
	        graphics,
	        tailFlag: c[SLOT.tailFlag],
	        tailMarker: c[SLOT.tailMarker],
	        tailHashMarker: c[SLOT.tailHashMarker],
	        pageProperties: pageProperties ?? c[SLOT.pageProperties],
	    };
	    return {
	        kind: 'page',
	        slots,
	        raw: root,
	    };
	}
	function decodeSingletonEntityList(node, typeCode, path, warnings, decodeItem) {
	    return (0, entity_list_1.decodeEntityList)(node, typeCode, path, warnings, (item, _i, itemPath) => decodeItem(item, itemPath));
	}
	function decodeConditionalSlot(node, path, warnings, decoder) {
	    if (node === undefined) {
	        return null;
	    }
	    if ((0, entity_list_1.isPlaceholderContainer)(node)) {
	        return null;
	    }
	    const decoded = decoder(node, path, warnings);
	    return decoded ?? node;
	}
	function decodeGraphicsSlot(node, path, warnings) {
	    if (node === undefined) {
	        return null;
	    }
	    if ((0, entity_list_1.isPlaceholderContainer)(node)) {
	        return null;
	    }
	    if (node.type !== 'container' && node.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `graphics slot is not a container (${node.type})`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const detected = (0, entity_list_1.detectEntityList)(node.children);
	    if (detected === null) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `graphics slot does not look like an entity-list`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    const typeCode = (0, typeCodes_1.asTypeCode)(detected.typeCode);
	    if (typeCode === null || !GRAPHICS_TYPE_CODES.has(typeCode)) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
	            message: `graphics slot holds unfamiliar typeCode=${detected.typeCode}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return node;
	    }
	    return (0, entity_list_1.decodeEntityList)(node, typeCode, path, warnings, (item, _i, itemPath) => (0, decode_graphics_annotation_1.decodeGraphicsAnnotation)(item, typeCode, itemPath, warnings));
	}
	function decodeOrRaw(node, path, warnings, decoder) {
	    if (node === undefined) {
	        return null;
	    }
	    const decoded = decoder(node, path, warnings);
	    return decoded ?? node;
	}
	
	return decodePageDocument;
}

var decodeMasterDocument = {};

var decodeMasterPageRef = {};

var hasRequiredDecodeMasterPageRef;

function requireDecodeMasterPageRef () {
	if (hasRequiredDecodeMasterPageRef) return decodeMasterPageRef;
	hasRequiredDecodeMasterPageRef = 1;
	Object.defineProperty(decodeMasterPageRef, "__esModule", { value: true });
	decodeMasterPageRef.decodeMasterPageRefs = decodeMasterPageRefs;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const entity_list_1 = requireEntityList();
	function decodeMasterPageRefs(slot, path, warnings) {
	    if (slot === undefined) {
	        return [];
	    }
	    if (slot.type !== 'container' && slot.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for Root[6], got ${slot.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return [];
	    }
	    const detected = (0, entity_list_1.detectEntityList)(slot.children);
	    if (detected === null) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `Root[6] does not open with (count, typeCode)`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return [];
	    }
	    if (detected.typeCode !== '6') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
	            message: `expected Root[6] typeCode=6, found ${detected.typeCode}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return [];
	    }
	    const out = [];
	    const items = (0, entity_list_1.entityListItems)(slot);
	    for (const [idx, item] of items.entries()) {
	        const itemPath = `${path}/items[${idx}]`;
	        if (item.type !== 'text') {
	            warnings.push({
	                type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	                message: `expected text row in Root[6], got ${item.type}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                path: itemPath,
	            });
	            continue;
	        }
	        const parsed = parsePageRefRow(item.content);
	        if (parsed === null) {
	            warnings.push({
	                type: decoded_types_1.DecodeWarningType.InvariantViolation,
	                message: `Root[6] row has fewer than 3 whitespace-separated fields: ${JSON.stringify(item.content)}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                path: itemPath,
	            });
	            continue;
	        }
	        out.push({ kind: 'master-page-ref', ...parsed, raw: item });
	    }
	    if (out.length !== detected.declaredCount) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `Root[6] declared count=${detected.declaredCount} but decoded ${out.length} rows`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path,
	        });
	    }
	    return out;
	}
	function parsePageRefRow(text) {
	    const trimmed = text.trim();
	    if (trimmed.length === 0) {
	        return null;
	    }
	    const tokens = trimmed.split(/\s+/);
	    if (tokens.length < 3) {
	        return null;
	    }
	    const pageNum = tokens[tokens.length - 1];
	    const pageFile = tokens[tokens.length - 2];
	    const pageName = tokens.slice(0, tokens.length - 2).join(' ');
	    return { pageName, pageFile, pageNum };
	}
	
	return decodeMasterPageRef;
}

var decodeMasterStyleAssignments = {};

var hasRequiredDecodeMasterStyleAssignments;

function requireDecodeMasterStyleAssignments () {
	if (hasRequiredDecodeMasterStyleAssignments) return decodeMasterStyleAssignments;
	hasRequiredDecodeMasterStyleAssignments = 1;
	Object.defineProperty(decodeMasterStyleAssignments, "__esModule", { value: true });
	decodeMasterStyleAssignments.decodeMasterStyleAssignments = decodeMasterStyleAssignments$1;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const entity_list_1 = requireEntityList();
	function decodeMasterStyleAssignments$1(slot, path, warnings) {
	    if (slot === undefined) {
	        return [];
	    }
	    if (slot.type !== 'container' && slot.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `expected container for Root[0], got ${slot.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return [];
	    }
	    const detected = (0, entity_list_1.detectEntityList)(slot.children);
	    if (detected === null) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	            message: `Root[0] does not open with (count, typeCode)`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return [];
	    }
	    if (detected.typeCode !== '12') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
	            message: `expected Root[0] typeCode=12, found ${detected.typeCode}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            path,
	        });
	        return [];
	    }
	    const out = [];
	    const items = (0, entity_list_1.entityListItems)(slot);
	    for (const [idx, item] of items.entries()) {
	        const itemPath = `${path}/items[${idx}]`;
	        if (item.type !== 'text') {
	            warnings.push({
	                type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
	                message: `expected text row in Root[0], got ${item.type}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                path: itemPath,
	            });
	            continue;
	        }
	        const parsed = parseStyleAssignmentRow(item.content);
	        if (parsed === null) {
	            warnings.push({
	                type: decoded_types_1.DecodeWarningType.InvariantViolation,
	                message: `Root[0] row has fewer than 3 whitespace-separated fields: ${JSON.stringify(item.content)}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                path: itemPath,
	            });
	            continue;
	        }
	        out.push({ kind: 'style-assignment', ...parsed, raw: item });
	    }
	    if (out.length !== detected.declaredCount) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `Root[0] declared count=${detected.declaredCount} but decoded ${out.length} rows`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path,
	        });
	    }
	    return out;
	}
	function parseStyleAssignmentRow(text) {
	    const tokens = text.trim().split(/\s+/);
	    if (tokens.length < 3) {
	        return null;
	    }
	    return { pageNum: tokens[0], rowIndex: tokens[1], styleName: tokens.slice(2).join(' ') };
	}
	
	return decodeMasterStyleAssignments;
}

var hasRequiredDecodeMasterDocument;

function requireDecodeMasterDocument () {
	if (hasRequiredDecodeMasterDocument) return decodeMasterDocument;
	hasRequiredDecodeMasterDocument = 1;
	Object.defineProperty(decodeMasterDocument, "__esModule", { value: true });
	decodeMasterDocument.decodeMasterDocument = decodeMasterDocument$1;
	decodeMasterDocument.buildInstanceHashMap = buildInstanceHashMap;
	decodeMasterDocument.buildInstanceHashSet = buildInstanceHashSet;
	decodeMasterDocument.buildNetHashMap = buildNetHashMap;
	decodeMasterDocument.buildNetHashSet = buildNetHashSet;
	const entity_list_1 = requireEntityList();
	const decode_master_page_ref_1 = requireDecodeMasterPageRef();
	const decode_master_style_assignments_1 = requireDecodeMasterStyleAssignments();
	const STYLE_ASSIGNMENTS_ROOT_INDEX = 0;
	const PAGE_REFS_ROOT_INDEX = 6;
	const HASH_PATTERN = /^\d{16,22}$/;
	function decodeMasterDocument$1(root, path, warnings) {
	    const rootSlots = root.children.map((slot, rootIndex) => decodeRootSlot(slot, rootIndex));
	    const pageRefs = (0, decode_master_page_ref_1.decodeMasterPageRefs)(root.children[PAGE_REFS_ROOT_INDEX], `${path}/root[${PAGE_REFS_ROOT_INDEX}]`, warnings);
	    const styleAssignments = (0, decode_master_style_assignments_1.decodeMasterStyleAssignments)(root.children[STYLE_ASSIGNMENTS_ROOT_INDEX], `${path}/root[${STYLE_ASSIGNMENTS_ROOT_INDEX}]`, warnings);
	    return {
	        kind: 'master',
	        rootSlots,
	        pageRefs,
	        styleAssignments,
	        raw: root,
	    };
	}
	function decodeRootSlot(slot, rootIndex, path, _warnings) {
	    if (slot.type !== 'container' && slot.type !== 'econtainer') {
	        return slot;
	    }
	    const detected = (0, entity_list_1.detectEntityList)(slot.children);
	    const declaredTypeCode = detected === null ? null : detected.typeCode;
	    const declaredCount = detected === null ? null : detected.declaredCount;
	    const itemCount = detected === null ? slot.children.length : (0, entity_list_1.entityListItems)(slot).length;
	    const hashes = collectHashes(slot);
	    return {
	        kind: 'master-root-slot',
	        rootIndex,
	        declaredTypeCode,
	        declaredCount,
	        itemCount,
	        hashes,
	        raw: slot,
	    };
	}
	function collectHashes(node) {
	    const out = new Set();
	    function walk(n) {
	        if (n.type === 'atom' || n.type === 'name' || n.type === 'value' || n.type === 'text') {
	            for (const piece of n.content.split(/\s+/)) {
	                if (HASH_PATTERN.test(piece)) {
	                    out.add(piece);
	                }
	            }
	            return;
	        }
	        if (n.type === 'container' || n.type === 'econtainer') {
	            for (const child of n.children) {
	                walk(child);
	            }
	        }
	    }
	    walk(node);
	    return [...out];
	}
	const MIN_HASH_LEN = 16;
	function buildInstanceHashMap(master) {
	    return buildKeyedTextRowMap(master, 11);
	}
	function buildInstanceHashSet(master) {
	    return buildHashSet(master, 11);
	}
	function buildNetHashMap(master) {
	    return buildKeyedTextRowMap(master, 12);
	}
	function buildNetHashSet(master) {
	    return buildHashSet(master, 12);
	}
	function buildKeyedTextRowMap(master, rootIndex) {
	    const out = new Map();
	    const slot = master.rootSlots[rootIndex];
	    if (!slot) {
	        return out;
	    }
	    const raw = 'raw' in slot ? slot.raw : slot;
	    if (raw.type !== 'container' && raw.type !== 'econtainer') {
	        return out;
	    }
	    for (const child of raw.children) {
	        if (child.type !== 'text') {
	            continue;
	        }
	        const tokens = child.content.trim().split(/\s+/);
	        if (tokens.length < 3) {
	            continue;
	        }
	        const [tok0, tok1, tok2, ...rest] = tokens;
	        if (isLongHash(tok2)) {
	            out.set(`${tok1}:${tok2}`, tok0);
	            continue;
	        }
	        if (isLongHash(tok1)) {
	            const value = [tok2, ...rest].join(' ');
	            out.set(`${tok0}:${tok1}`, value);
	            continue;
	        }
	    }
	    return out;
	}
	function buildHashSet(master, rootIndex) {
	    const out = new Set();
	    const slot = master.rootSlots[rootIndex];
	    if (!slot) {
	        return out;
	    }
	    const raw = 'raw' in slot ? slot.raw : slot;
	    if (raw.type !== 'container' && raw.type !== 'econtainer') {
	        return out;
	    }
	    function walk(node) {
	        switch (node.type) {
	            case 'atom':
	            case 'name':
	            case 'value':
	            case 'text':
	                for (const piece of node.content.split(/\s+/)) {
	                    if (isLongHash(piece)) {
	                        out.add(piece);
	                    }
	                }
	                return;
	            case 'raw-blob':
	                return;
	            case 'container':
	            case 'econtainer':
	                for (const child of node.children) {
	                    walk(child);
	                }
	                return;
	        }
	    }
	    walk(raw);
	    return out;
	}
	function isLongHash(s) {
	    return s.length >= MIN_HASH_LEN && /^\d+$/.test(s);
	}
	
	return decodeMasterDocument;
}

var hasRequiredDecodeDocument;

function requireDecodeDocument () {
	if (hasRequiredDecodeDocument) return decodeDocument;
	hasRequiredDecodeDocument = 1;
	Object.defineProperty(decodeDocument, "__esModule", { value: true });
	decodeDocument.decodeDocument = decodeDocument$1;
	const decoded_types_1 = requireDecodedTypes();
	const sigma_engine_shared_1 = requireDist$1();
	const decode_page_document_1 = requireDecodePageDocument();
	const decode_master_document_1 = requireDecodeMasterDocument();
	function decodeDocument$1(doc, context) {
	    const warnings = [];
	    const raw = doc.nodes[0];
	    if (doc.nodes.length < 3) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `expected at least 3 top-level nodes, got ${doc.nodes.length}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path: '/document',
	        });
	        return makeUnknownDocument(doc, NaN, NaN, warnings);
	    }
	    const versionAtom = doc.nodes[0];
	    const typeAtom = doc.nodes[1];
	    const root = doc.nodes[2];
	    if (versionAtom.type !== 'atom' || !versionAtom.content.startsWith('version')) {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `expected version atom, got ${versionAtom.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path: '/document/nodes[0]',
	        });
	        return makeUnknownDocument(doc, NaN, NaN, warnings);
	    }
	    if (typeAtom.type !== 'atom') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `expected rootTypeCode atom, got ${typeAtom.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path: '/document/nodes[1]',
	        });
	        return makeUnknownDocument(doc, NaN, NaN, warnings);
	    }
	    if (root.type !== 'container' && root.type !== 'econtainer') {
	        warnings.push({
	            type: decoded_types_1.DecodeWarningType.InvariantViolation,
	            message: `expected root container, got ${root.type}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            path: '/document/nodes[2]',
	        });
	        return makeUnknownDocument(doc, NaN, NaN, warnings);
	    }
	    const schemaVersion = parseInt(versionAtom.content.replace(/^version\s*/, ''), 10);
	    const rootTypeCode = parseInt(typeAtom.content, 10);
	    const ctx = context ?? { instanceHashMap: null, netHashMap: null, pageNum: null };
	    if (rootTypeCode === 4) {
	        const body = (0, decode_page_document_1.decodePageDocument)(root, '/page', warnings, ctx.instanceHashMap, ctx.pageNum);
	        return {
	            kind: 'document',
	            schemaVersion,
	            rootTypeCode,
	            body,
	            warnings,
	            raw,
	        };
	    }
	    if (rootTypeCode === 3) {
	        const body = (0, decode_master_document_1.decodeMasterDocument)(root, '/master', warnings);
	        return {
	            kind: 'document',
	            schemaVersion,
	            rootTypeCode,
	            body,
	            warnings,
	            raw,
	        };
	    }
	    warnings.push({
	        type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
	        message: `unrecognized rootTypeCode=${rootTypeCode}`,
	        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        path: '/document',
	    });
	    return makeUnknownDocument(doc, schemaVersion, rootTypeCode, warnings);
	}
	function makeUnknownDocument(doc, schemaVersion, rootTypeCode, warnings) {
	    const root = doc.nodes[2] ?? doc.nodes[0];
	    const body = {
	        kind: 'unknown-document',
	        rootTypeCode,
	        raw: root,
	    };
	    return {
	        kind: 'document',
	        schemaVersion,
	        rootTypeCode,
	        body,
	        warnings,
	        raw: root,
	    };
	}
	
	return decodeDocument;
}

var chipsPrtParser = {};

var hasRequiredChipsPrtParser;

function requireChipsPrtParser () {
	if (hasRequiredChipsPrtParser) return chipsPrtParser;
	hasRequiredChipsPrtParser = 1;
	Object.defineProperty(chipsPrtParser, "__esModule", { value: true });
	chipsPrtParser.parseChipsPrt = parseChipsPrt;
	function parseChipsPrt(content) {
	    const parts = new Map();
	    let currentPart = null;
	    let inPinSection = false;
	    let currentPinName = null;
	    for (const rawLine of content.split('\n')) {
	        const line = rawLine.trim();
	        if (line.length === 0) {
	            continue;
	        }
	        const primitiveMatch = /^primitive\s+'([^']+)'\s*;?\s*$/i.exec(line);
	        if (primitiveMatch !== null) {
	            const name = primitiveMatch[1].toLowerCase();
	            currentPart = { name, pinNumberByName: new Map() };
	            parts.set(name, currentPart);
	            inPinSection = false;
	            currentPinName = null;
	            continue;
	        }
	        if (/^end_primitive\s*;?\s*$/i.test(line)) {
	            currentPart = null;
	            inPinSection = false;
	            currentPinName = null;
	            continue;
	        }
	        if (currentPart === null) {
	            continue;
	        }
	        if (/^pin\b/i.test(line)) {
	            inPinSection = true;
	            continue;
	        }
	        if (/^end_pin\s*;?\s*$/i.test(line)) {
	            inPinSection = false;
	            currentPinName = null;
	            continue;
	        }
	        if (!inPinSection) {
	            continue;
	        }
	        const pinHeaderMatch = /^'([^']+)'\s*:\s*$/.exec(line);
	        if (pinHeaderMatch !== null) {
	            currentPinName = pinHeaderMatch[1];
	            continue;
	        }
	        if (currentPinName === null) {
	            continue;
	        }
	        const pinNumberMatch = /^PIN_NUMBER\s*=\s*'\(?([^')]+)\)?'\s*;?\s*$/i.exec(line);
	        if (pinNumberMatch !== null) {
	            currentPart.pinNumberByName.set(currentPinName.toLowerCase(), pinNumberMatch[1]);
	        }
	    }
	    return { parts };
	}
	
	return chipsPrtParser;
}

var styleParser = {};

var hasRequiredStyleParser;

function requireStyleParser () {
	if (hasRequiredStyleParser) return styleParser;
	hasRequiredStyleParser = 1;
	Object.defineProperty(styleParser, "__esModule", { value: true });
	styleParser.StyleParseWarningType = void 0;
	styleParser.parseStyleFile = parseStyleFile;
	const sigma_engine_shared_1 = requireDist$1();
	var StyleParseWarningType;
	(function (StyleParseWarningType) {
	    StyleParseWarningType["UnrecognizedLine"] = "allegro-style-unrecognized-line";
	    StyleParseWarningType["UnterminatedBlock"] = "allegro-style-unterminated-block";
	})(StyleParseWarningType || (styleParser.StyleParseWarningType = StyleParseWarningType = {}));
	const HEADER_RE = /^(Style\d+)\s*\{\s*$/;
	const KV_RE = /^([a-zA-Z][a-zA-Z0-9_-]*)\s*:\s*(.*?)\s*$/;
	const NUMERIC_RE = /^-?\d+(\.\d+)?$/;
	function parseStyleFile(content) {
	    const styles = new Map();
	    const warnings = [];
	    let current = null;
	    const lines = content.split('\n');
	    for (let i = 0; i < lines.length; i++) {
	        const line = lines[i].trim();
	        if (line.length === 0 || line.startsWith('#') || line.startsWith('//')) {
	            continue;
	        }
	        if (current === null) {
	            const headerMatch = HEADER_RE.exec(line);
	            if (headerMatch !== null) {
	                current = makeEmptyStyle(headerMatch[1]);
	                continue;
	            }
	            continue;
	        }
	        if (line === '}') {
	            styles.set(current.name, current);
	            current = null;
	            continue;
	        }
	        const kv = KV_RE.exec(line);
	        if (kv === null) {
	            warnings.push({
	                type: StyleParseWarningType.UnrecognizedLine,
	                message: `line ${i + 1}: unrecognized line inside ${current.name}: ${JSON.stringify(line)}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            continue;
	        }
	        applyKeyValue(current, kv[1], kv[2]);
	    }
	    if (current !== null) {
	        warnings.push({
	            type: StyleParseWarningType.UnterminatedBlock,
	            message: `unterminated style block ${current.name} (missing closing brace)`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	        });
	    }
	    return { styles, warnings };
	}
	function makeEmptyStyle(name) {
	    return {
	        name,
	        lineWidth: null,
	        lineColor: null,
	        lineStyle: null,
	        lineCapStyle: null,
	        lineJoinStyle: null,
	        fillColor: null,
	        fillStyle: null,
	        altFillColor: null,
	        headerFillColor: null,
	        fontName: null,
	        fontSize: null,
	        italic: null,
	        bold: null,
	        underline: null,
	        fontColor: null,
	        textMargin: null,
	        textWordwrap: null,
	        itemOpacity: null,
	        instPropFontSize: null,
	        pinPropFontSize: null,
	        customImageMinimumLineWidth: null,
	        extras: {},
	    };
	}
	function applyKeyValue(style, key, value) {
	    switch (key) {
	        case 'line-width':
	            style.lineWidth = parseNumber(value);
	            return;
	        case 'line-color':
	            style.lineColor = value;
	            return;
	        case 'line-style':
	            style.lineStyle = value;
	            return;
	        case 'line-cap-style':
	            style.lineCapStyle = value;
	            return;
	        case 'line-join-style':
	            style.lineJoinStyle = value;
	            return;
	        case 'fill-color':
	            style.fillColor = value;
	            return;
	        case 'fill-style':
	            style.fillStyle = value;
	            return;
	        case 'alt-fill-color':
	            style.altFillColor = value;
	            return;
	        case 'header-fill-color':
	            style.headerFillColor = value;
	            return;
	        case 'font-name':
	            style.fontName = value;
	            return;
	        case 'font-size':
	            style.fontSize = parseNumber(value);
	            return;
	        case 'italic':
	            style.italic = parseBool(value);
	            return;
	        case 'bold':
	            style.bold = parseBool(value);
	            return;
	        case 'underline':
	            style.underline = parseBool(value);
	            return;
	        case 'font-color':
	            style.fontColor = value;
	            return;
	        case 'text-margin':
	            style.textMargin = parseNumber(value);
	            return;
	        case 'text-wordwrap':
	            style.textWordwrap = parseBool(value);
	            return;
	        case 'item-opacity':
	            style.itemOpacity = parseNumber(value);
	            return;
	        case 'inst-prop-font-size':
	            style.instPropFontSize = parseNumber(value);
	            return;
	        case 'pin-prop-font-size':
	            style.pinPropFontSize = parseNumber(value);
	            return;
	        case 'custom-image-minimum-line-width':
	            style.customImageMinimumLineWidth = parseNumber(value);
	            return;
	        default:
	            style.extras[key] = value;
	            return;
	    }
	}
	function parseNumber(value) {
	    if (!NUMERIC_RE.test(value)) {
	        return null;
	    }
	    const n = parseFloat(value);
	    return Number.isFinite(n) ? n : null;
	}
	function parseBool(value) {
	    const v = value.toLowerCase();
	    if (v === 'true') {
	        return true;
	    }
	    if (v === 'false') {
	        return false;
	    }
	    return null;
	}
	
	return styleParser;
}

var dcfParser = {};

var hasRequiredDcfParser;

function requireDcfParser () {
	if (hasRequiredDcfParser) return dcfParser;
	hasRequiredDcfParser = 1;
	Object.defineProperty(dcfParser, "__esModule", { value: true });
	dcfParser.parseDcf = parseDcf;
	dcfParser.parseDcfSectionMap = parseDcfSectionMap;
	const fflate_1 = /*@__PURE__*/ requireBrowser();
	const GATE_XML_PATH = 'objects/Gate.xml';
	const PIN_XML_PATH = 'objects/Pin.xml';
	const NET_XML_PATH = 'objects/Net.xml';
	const XCON_ID_RE = /\\(I\d+)\\/;
	const XCON_NET_ID_RE = /\\(N\d+)\\/;
	const PIN_A_ATTR_RE = /^[^.]+\.(.+)$/;
	function parseDcf(dcfBytes) {
	    const sectionMap = new Map();
	    const instancePinMap = new Map();
	    const instanceAttributes = new Map();
	    const netAttributesByPhysName = new Map();
	    const physNetNameByXconId = new Map();
	    let unzipped;
	    try {
	        unzipped = (0, fflate_1.unzipSync)(dcfBytes);
	    }
	    catch {
	        return { sectionMap, instancePinMap, instanceAttributes, netAttributesByPhysName, physNetNameByXconId };
	    }
	    const gateEntry = unzipped[GATE_XML_PATH];
	    if (gateEntry !== undefined) {
	        collectGateEntries(new TextDecoder('utf-8').decode(gateEntry), sectionMap, instanceAttributes);
	    }
	    const pinEntry = unzipped[PIN_XML_PATH];
	    if (pinEntry !== undefined) {
	        collectPinEntries(new TextDecoder('utf-8').decode(pinEntry), instancePinMap);
	    }
	    const netEntry = unzipped[NET_XML_PATH];
	    if (netEntry !== undefined) {
	        collectNetEntries(new TextDecoder('utf-8').decode(netEntry), netAttributesByPhysName, physNetNameByXconId);
	    }
	    return { sectionMap, instancePinMap, instanceAttributes, netAttributesByPhysName, physNetNameByXconId };
	}
	function parseDcfSectionMap(dcfBytes) {
	    return parseDcf(dcfBytes).sectionMap;
	}
	const ATTR_RE = /<a\s+N="([^"]+)"[^>]*>\s*<v\s+V="([^"]*)"/g;
	function collectGateEntries(xml, secOut, attrsOut) {
	    const oTagRe = /<o\s[^>]*N="([^"]+)"[^>]*>/g;
	    let oMatch;
	    while ((oMatch = oTagRe.exec(xml)) !== null) {
	        const nAttr = oMatch[1];
	        const idMatch = XCON_ID_RE.exec(nAttr);
	        if (idMatch === null) {
	            continue;
	        }
	        const xconId = idMatch[1];
	        const searchFrom = oMatch.index + oMatch[0].length;
	        const searchTo = Math.min(searchFrom + 4096, xml.length);
	        const window = xml.slice(searchFrom, searchTo);
	        const nextO = window.indexOf('<o ');
	        const searchWindow = nextO >= 0 ? window.slice(0, nextO) : window;
	        const attrs = {};
	        ATTR_RE.lastIndex = 0;
	        let attrMatch;
	        while ((attrMatch = ATTR_RE.exec(searchWindow)) !== null) {
	            attrs[attrMatch[1]] = attrMatch[2];
	        }
	        if (Object.keys(attrs).length > 0) {
	            attrsOut.set(xconId, attrs);
	        }
	        const secStr = attrs['SEC'];
	        if (secStr !== undefined) {
	            const sec = parseInt(secStr, 10);
	            if (!isNaN(sec)) {
	                secOut.set(xconId, sec);
	            }
	        }
	    }
	}
	function collectPinEntries(xml, out) {
	    const oTagRe = /<o\s[^>]*N="([^"]+)"[^>]*A="([^"]+)"[^>]*>/g;
	    let oMatch;
	    while ((oMatch = oTagRe.exec(xml)) !== null) {
	        const nAttr = oMatch[1];
	        const aAttr = oMatch[2];
	        const idMatch = XCON_ID_RE.exec(nAttr);
	        if (idMatch === null) {
	            continue;
	        }
	        const xconId = idMatch[1];
	        const pinNameMatch = PIN_A_ATTR_RE.exec(aAttr);
	        if (pinNameMatch === null) {
	            continue;
	        }
	        const pinName = pinNameMatch[1];
	        const searchFrom = oMatch.index + oMatch[0].length;
	        const searchTo = Math.min(searchFrom + 512, xml.length);
	        const window = xml.slice(searchFrom, searchTo);
	        const pnMatch = /<a\s+N="PN"[^>]*>\s*<v\s+V="([^"]+)"/.exec(window);
	        if (pnMatch === null) {
	            continue;
	        }
	        const pn = pnMatch[1].trim();
	        if (pn.length === 0) {
	            continue;
	        }
	        let pinsByName = out.get(xconId);
	        if (pinsByName === undefined) {
	            pinsByName = new Map();
	            out.set(xconId, pinsByName);
	        }
	        pinsByName.set(pinName, pn);
	    }
	}
	function collectNetEntries(xml, out, physNetNameOut) {
	    const oTagRe = /<o\s[^>]*N="([^"]+)"[^>]*>/g;
	    let oMatch;
	    while ((oMatch = oTagRe.exec(xml)) !== null) {
	        const nAttr = oMatch[1];
	        const idMatch = XCON_NET_ID_RE.exec(nAttr);
	        if (idMatch === null) {
	            continue;
	        }
	        const xconId = idMatch[1];
	        const searchFrom = oMatch.index + oMatch[0].length;
	        const searchTo = Math.min(searchFrom + 2048, xml.length);
	        const window = xml.slice(searchFrom, searchTo);
	        const nextO = window.indexOf('<o ');
	        const searchWindow = nextO >= 0 ? window.slice(0, nextO) : window;
	        const attrs = {};
	        ATTR_RE.lastIndex = 0;
	        let attrMatch;
	        while ((attrMatch = ATTR_RE.exec(searchWindow)) !== null) {
	            attrs[attrMatch[1]] = attrMatch[2];
	        }
	        const physNetName = attrs['PHYS_NET_NAME'];
	        if (physNetName === undefined || physNetName.length === 0) {
	            continue;
	        }
	        physNetNameOut.set(xconId, physNetName);
	        const { PHYS_NET_NAME: _, ...rest } = attrs;
	        if (Object.keys(rest).length > 0) {
	            out.set(physNetName, rest);
	        }
	    }
	}
	
	return dcfParser;
}

var hasRequiredParseAllegroProject;

function requireParseAllegroProject () {
	if (hasRequiredParseAllegroProject) return parseAllegroProject;
	hasRequiredParseAllegroProject = 1;
	Object.defineProperty(parseAllegroProject, "__esModule", { value: true });
	parseAllegroProject.parseProject = parseProject;
	const sigma_engine_shared_1 = requireDist$1();
	const allegroValidation_1 = requireAllegroValidation();
	const AllegroOrchestratedTransformer_1 = requireAllegroOrchestratedTransformer();
	const AllegroResolveDesign_1 = requireAllegroResolveDesign();
	const sdaxArchive_1 = requireSdaxArchive();
	const cdszArchive_1 = requireCdszArchive();
	const AllegroNetlistParser_1 = requireAllegroNetlistParser();
	const AllegroPartListParser_1 = requireAllegroPartListParser();
	const xconParser_1 = requireXconParser();
	const dxJsonParser_1 = requireDxJsonParser();
	const parser_1 = requireParser();
	const decode_document_1 = requireDecodeDocument();
	const decode_master_document_1 = requireDecodeMasterDocument();
	const AllegroRecordHelpers_1 = requireAllegroRecordHelpers();
	const helpers_1 = requireHelpers();
	const cssParser_1 = requireCssParser();
	const chipsPrtParser_1 = requireChipsPrtParser();
	const styleParser_1 = requireStyleParser();
	const dcfParser_1 = requireDcfParser();
	function getDefaultSettings() {
	    return {
	        transformOpts: {},
	        allowPartialUpload: false,
	    };
	}
	async function parseProject(def, initialSettings = {}) {
	    const settings = { ...getDefaultSettings(), ...initialSettings };
	    if (def.type !== 'allegro') {
	        throw new Error(`Attempted to parse non-Allegro project def: ${def.type}`);
	    }
	    const parsedCpm = pullParsedCpm(def);
	    const parsedCdsLib = pullParsedCdsLib(def);
	    const sdaxBlob = await pullSdaxBytes(def);
	    const cdszBlob = await pullCdszBytes(def);
	    let packagerNetlist;
	    let packagerPartList;
	    if (cdszBlob !== null) {
	        const extractedCdsz = (0, cdszArchive_1.extractCdsz)(cdszBlob);
	        const netlistEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extractedCdsz, 'pstxnet.dat');
	        if (netlistEntry) {
	            packagerNetlist = (0, AllegroNetlistParser_1.parseAllegroNetlist)((0, cdszArchive_1.readCdszEntryAsText)(netlistEntry));
	        }
	        const partListEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extractedCdsz, 'pstxprt.dat');
	        if (partListEntry) {
	            packagerPartList = (0, AllegroPartListParser_1.parseAllegroPartList)((0, cdszArchive_1.readCdszEntryAsText)(partListEntry));
	        }
	    }
	    const symbolCssByCell = await pullSymbolCssMap(def, parsedCdsLib);
	    const chipsPrtByCellName = await pullChipsPrtMap(def);
	    let extractedSdax = null;
	    let parsedXcon = null;
	    let parsedDx = null;
	    let masterBundle = null;
	    const sheetPages = [];
	    if (sdaxBlob !== null) {
	        extractedSdax = (0, sdaxArchive_1.extractSdax)(sdaxBlob);
	        parsedXcon = parseXconFromSdax(extractedSdax, parsedCpm.designName);
	        parsedDx = parseDxFromSdax(extractedSdax, parsedCpm.designName);
	        masterBundle = decodeMasterFromSdax(extractedSdax, parsedCpm.designName);
	        sheetPages.push(...decodeSheetPagesFromSdax(extractedSdax, masterBundle, settings.allowPartialUpload));
	    }
	    const { styleMap: styles, styleWarnings } = await pullStyleMap(def, extractedSdax);
	    if (!settings.allowPartialUpload) {
	        assertCompleteUpload({ parsedCpm, parsedCdsLib, sdaxBlob, extractedSdax, masterBundle, sheetPages });
	    }
	    const instanceHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildInstanceHashMap)(masterBundle.body);
	    const netHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildNetHashMap)(masterBundle.body);
	    const styleAssignmentByKey = buildStyleAssignmentMap(masterBundle?.body ?? null);
	    const parsedDcf = extractedSdax !== null ? parseDcfFromSdax(extractedSdax, parsedCpm.designName) : null;
	    const instanceSectionMap = parsedDcf?.sectionMap ?? new Map();
	    const instancePinMap = parsedDcf?.instancePinMap ?? new Map();
	    const instanceAttributes = parsedDcf?.instanceAttributes ?? new Map();
	    const netAttributesByName = parsedDcf?.netAttributesByPhysName ?? new Map();
	    const physNetNameByXconId = parsedDcf?.physNetNameByXconId ?? new Map();
	    const xconPinNetByInstance = buildXconPinNetByInstance(parsedXcon, physNetNameByXconId);
	    const cellInstanceIds = buildCellInstanceIds(sheetPages, instanceHashMap);
	    const resolved = (0, AllegroResolveDesign_1.resolveAllegroProject)({
	        projectName: parsedCpm.designName,
	        parsedXcon,
	        parsedDx,
	        instanceHashMap,
	        netHashMap,
	        instanceSectionMap,
	        instancePinMap,
	        instanceAttributes,
	        netAttributesByName,
	        physNetNameByXconId,
	        xconPinNetByInstance,
	        cellInstanceIds,
	        symbolCssByCell,
	        chipsPrtByCellName,
	        styles,
	        styleAssignmentByKey,
	        sheetPages,
	    });
	    const rootContext = {
	        parsedDef: def,
	        options: settings.transformOpts,
	        parsedCpm,
	        parsedCdsLib,
	        parsedXcon,
	        parsedDx,
	        decodedMaster: masterBundle?.body ?? null,
	        instanceHashMap,
	        netHashMap,
	        instanceSectionMap,
	        instancePinMap,
	        instanceAttributes,
	        netAttributesByName,
	        physNetNameByXconId,
	        xconPinNetByInstance,
	        cellInstanceIds,
	        sheetPages,
	        symbolCssByCell,
	        chipsPrtByCellName,
	        styles,
	        styleAssignmentByKey,
	        componentsByCellName: new Map(),
	        resolved,
	        resolveImageBytes: buildImageResolver(extractedSdax),
	        packagerNetlist,
	        packagerPartList,
	    };
	    const transformResult = (0, AllegroOrchestratedTransformer_1.transformAllegroProject)(rootContext);
	    const projectModel = transformResult.projectModels[0] ?? null;
	    if (projectModel !== null) {
	        attachParseResults(projectModel, masterBundle, sheetPages, packagerNetlist, packagerPartList, styleWarnings);
	    }
	    return {
	        projectModel,
	        extractedImages: transformResult.extractedImages,
	    };
	}
	function buildXconPinNetByInstance(parsedXcon, physNetNameByXconId) {
	    const out = new Map();
	    const design = parsedXcon?.designs[0];
	    if (design === undefined) {
	        return out;
	    }
	    for (const instance of design.instances.values()) {
	        const cell = design.cells.get(instance.cellId);
	        if (cell === undefined) {
	            continue;
	        }
	        const termIdToName = new Map();
	        for (const term of cell.terms) {
	            if (term.id.length > 0 && term.name.length > 0) {
	                termIdToName.set(term.id, term.name.toLowerCase());
	            }
	        }
	        const pinNetMap = new Map();
	        for (const pin of instance.pins) {
	            const pinName = termIdToName.get(pin.termId);
	            if (pinName === undefined) {
	                continue;
	            }
	            const netId = pin.netIds[0];
	            if (netId === undefined || netId.length === 0) {
	                continue;
	            }
	            const netName = physNetNameByXconId.get(netId) ?? design.nets.get(netId)?.name;
	            if (netName === undefined || netName.length === 0) {
	                continue;
	            }
	            pinNetMap.set(pinName, netName);
	        }
	        if (pinNetMap.size > 0) {
	            out.set(instance.id, pinNetMap);
	        }
	    }
	    return out;
	}
	function buildCellInstanceIds(sheetPages, instanceHashMap) {
	    const result = new Map();
	    for (const page of sheetPages) {
	        for (const instance of (0, AllegroRecordHelpers_1.iterateComponentInstances)(page.body)) {
	            const key = `${page.pageNum}:${instance.placementHash}`;
	            const xconId = instanceHashMap.get(key);
	            if (xconId === undefined) {
	                continue;
	            }
	            const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(instance.modelRef);
	            let ids = result.get(cellName);
	            if (ids === undefined) {
	                ids = [];
	                result.set(cellName, ids);
	            }
	            if (!ids.includes(xconId)) {
	                ids.push(xconId);
	            }
	        }
	    }
	    return result;
	}
	function assertCompleteUpload(state) {
	    const errors = [];
	    if (state.parsedCpm.designName === '' || state.parsedCpm.designDb === '') {
	        errors.push('missing or invalid `.cpm` (no design_name / design_db)');
	    }
	    if (state.parsedCdsLib.defines.size === 0 && state.parsedCdsLib.includes.length === 0) {
	        errors.push('missing or empty `cds.lib`');
	    }
	    if (state.sdaxBlob === null || state.extractedSdax === null) {
	        errors.push('missing `.sdax` schematic database');
	    }
	    if (state.extractedSdax !== null) {
	        if (state.masterBundle === null) {
	            errors.push('`.sdax` is missing the master `<design>.ascii` file');
	        }
	        else if (state.masterBundle.body.pageRefs.length === 0) {
	            errors.push('master `<design>.ascii` page table is empty');
	        }
	        else {
	            const presentPageBasenames = new Set();
	            for (const pageEntry of state.sheetPages) {
	                presentPageBasenames.add(pageEntry.pageFileName);
	            }
	            const missingPages = [];
	            for (const ref of state.masterBundle.body.pageRefs) {
	                if (!presentPageBasenames.has(ref.pageFile)) {
	                    missingPages.push(`${ref.pageName}(#${ref.pageNum}) → ${ref.pageFile}`);
	                }
	            }
	            if (missingPages.length > 0) {
	                errors.push(`master references ${missingPages.length} missing sheet(s): ${missingPages.join(', ')}`);
	            }
	            const referencedPageBasenames = new Set(state.masterBundle.body.pageRefs.map((r) => r.pageFile));
	            const orphans = Array.from(presentPageBasenames).filter((p) => !referencedPageBasenames.has(p));
	            if (orphans.length > 0) {
	                errors.push(`${orphans.length} sheet(s) present but not referenced by master: ${orphans.join(', ')}`);
	            }
	        }
	    }
	    if (errors.length > 0) {
	        throw new Error(`Allegro X System Capture upload is incomplete:\n  - ${errors.join('\n  - ')}\n` +
	            'Pass { allowPartialUpload: true } if intentionally parsing a partial archive.');
	    }
	}
	async function pullSymbolCssMap(def, parsedCdsLib) {
	    const out = new Map();
	    if (def.other === undefined) {
	        return out;
	    }
	    const libraryByPathPrefix = new Map();
	    for (const [libName, libPath] of parsedCdsLib.defines.entries()) {
	        const normalized = libPath.replace(/\/+$/, '');
	        libraryByPathPrefix.set(normalized, libName);
	    }
	    for (const descriptor of Object.values(def.other)) {
	        if (descriptor.validationResult?.data?.fileFormat.type !== allegroValidation_1.AllegroFileType.Css) {
	            continue;
	        }
	        const relPath = descriptor.originalFileName;
	        if (!relPath.toLowerCase().endsWith('/symbol.css')) {
	            continue;
	        }
	        const segments = relPath.split('/');
	        if (segments.length < 4) {
	            continue;
	        }
	        const view = (0, helpers_1.decodeCadenceName)(segments[segments.length - 2]);
	        const name = (0, helpers_1.decodeCadenceName)(segments[segments.length - 3]);
	        const libraryDir = segments.slice(0, segments.length - 3).join('/');
	        const libraryName = libraryByPathPrefix.get(libraryDir) ?? libraryByPathPrefix.get(libraryDir.replace(/^.*\//, ''));
	        if (libraryName === undefined) {
	            continue;
	        }
	        const text = await descriptor.contents.text();
	        out.set(`${libraryName}__${name}__${view}`, (0, cssParser_1.parseSymbolCss)(text));
	    }
	    return out;
	}
	async function pullChipsPrtMap(def) {
	    const out = new Map();
	    if (def.other === undefined) {
	        return out;
	    }
	    for (const descriptor of Object.values(def.other)) {
	        if (descriptor.validationResult?.data?.fileFormat.type !== allegroValidation_1.AllegroFileType.ChipsPrt) {
	            continue;
	        }
	        const text = await descriptor.contents.text();
	        const parsed = (0, chipsPrtParser_1.parseChipsPrt)(text);
	        for (const [name, part] of parsed.parts) {
	            out.set(name, part);
	        }
	    }
	    return out;
	}
	async function pullStyleMap(def, extractedSdax) {
	    const design = new Map();
	    const cells = new Map();
	    const styleWarnings = [];
	    const ingest = (path, text) => {
	        const parsed = (0, styleParser_1.parseStyleFile)(text);
	        styleWarnings.push(...parsed.warnings);
	        const scopeKey = scopeForStylePath(path);
	        if (scopeKey === 'design') {
	            for (const [name, sd] of parsed.styles) {
	                design.set(name, sd);
	            }
	        }
	        else {
	            let cellMap = cells.get(scopeKey);
	            if (cellMap === undefined) {
	                cellMap = new Map();
	                cells.set(scopeKey, cellMap);
	            }
	            for (const [name, sd] of parsed.styles) {
	                cellMap.set(name, sd);
	            }
	        }
	    };
	    for (const descriptor of Object.values(def.other ?? {})) {
	        if (descriptor.validationResult?.data?.fileFormat.type !== allegroValidation_1.AllegroFileType.Style) {
	            continue;
	        }
	        const text = await descriptor.contents.text();
	        ingest(descriptor.originalFileName, text);
	    }
	    if (extractedSdax !== null) {
	        for (const innerPath of (0, sdaxArchive_1.findEntriesByExtension)(extractedSdax, '.style')) {
	            const entry = extractedSdax.entries.get(innerPath);
	            if (entry === undefined) {
	                continue;
	            }
	            ingest(innerPath, (0, sdaxArchive_1.readEntryAsText)(entry));
	        }
	    }
	    return { styleMap: { design, cells }, styleWarnings };
	}
	function scopeForStylePath(relPath) {
	    const lower = relPath.toLowerCase().replace(/\\/g, '/');
	    if (/(^|\/)worklib\//.test(lower)) {
	        return 'design';
	    }
	    const base = relPath.split('/').pop() ?? relPath;
	    return `cell:${base.replace(/\.style$/i, '').toLowerCase()}`;
	}
	function buildStyleAssignmentMap(master) {
	    const out = new Map();
	    if (master === null) {
	        return out;
	    }
	    for (const row of master.styleAssignments) {
	        out.set(`${row.pageNum}:${row.rowIndex}`, row.styleName);
	    }
	    return out;
	}
	function pullParsedCpm(def) {
	    for (const descriptor of Object.values(def.other ?? {})) {
	        const data = descriptor.validationResult?.data;
	        if (data?.fileFormat.type === allegroValidation_1.AllegroFileType.Cpm) {
	            const parsed = data.metadata[allegroValidation_1.AllegroProjectMetadataKey.ParsedCpm];
	            if (parsed !== undefined) {
	                return parsed;
	            }
	        }
	    }
	    return {
	        sections: new Map(),
	        designName: '',
	        designLibrary: '',
	        libraries: [],
	        cpmVersion: '',
	        designDb: '',
	    };
	}
	function pullParsedCdsLib(def) {
	    for (const descriptor of Object.values(def.other ?? {})) {
	        const data = descriptor.validationResult?.data;
	        if (data?.fileFormat.type === allegroValidation_1.AllegroFileType.CdsLib) {
	            const parsed = data.metadata[allegroValidation_1.AllegroProjectMetadataKey.ParsedCdsLib];
	            if (parsed !== undefined) {
	                return parsed;
	            }
	        }
	    }
	    return { defines: new Map(), includes: [] };
	}
	async function pullSdaxBytes(def) {
	    for (const descriptor of Object.values(def.schematics)) {
	        if (isSdaxDescriptor(descriptor)) {
	            const buffer = await descriptor.contents.arrayBuffer();
	            return new Uint8Array(buffer);
	        }
	    }
	    for (const descriptor of Object.values(def.other ?? {})) {
	        if (isSdaxDescriptor(descriptor)) {
	            const buffer = await descriptor.contents.arrayBuffer();
	            return new Uint8Array(buffer);
	        }
	    }
	    return null;
	}
	function isSdaxDescriptor(descriptor) {
	    return descriptor.validationResult?.data?.fileFormat.type === allegroValidation_1.AllegroFileType.Sdax;
	}
	async function pullCdszBytes(def) {
	    for (const descriptor of Object.values(def.other ?? {})) {
	        if (descriptor.validationResult?.data?.fileFormat.type === allegroValidation_1.AllegroFileType.Cdsz) {
	            const buffer = await descriptor.contents.arrayBuffer();
	            return new Uint8Array(buffer);
	        }
	    }
	    return null;
	}
	function parseXconFromSdax(extracted, designName) {
	    const candidates = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.xcon');
	    if (candidates.length === 0) {
	        return null;
	    }
	    let selectedPath = null;
	    if (designName.length > 0) {
	        const wantedSuffix = `${designName.toLowerCase()}.xcon`;
	        for (const path of candidates) {
	            if (path.toLowerCase().endsWith(wantedSuffix)) {
	                selectedPath = path;
	                break;
	            }
	        }
	    }
	    selectedPath ??= candidates[0];
	    const entry = extracted.entries.get(selectedPath);
	    if (entry === undefined) {
	        return null;
	    }
	    return (0, xconParser_1.parseXcon)((0, sdaxArchive_1.readEntryAsText)(entry));
	}
	function parseDxFromSdax(extracted, designName) {
	    const candidates = (0, sdaxArchive_1.findEntriesByExtension)(extracted, 'dx.json');
	    if (candidates.length === 0) {
	        return null;
	    }
	    let selectedPath = null;
	    if (designName.length > 0) {
	        const wantedSuffix = `${designName.toLowerCase()}dx.json`;
	        for (const path of candidates) {
	            if (path.toLowerCase().endsWith(wantedSuffix)) {
	                selectedPath = path;
	                break;
	            }
	        }
	    }
	    selectedPath ??= candidates[0];
	    const entry = extracted.entries.get(selectedPath);
	    if (entry === undefined) {
	        return null;
	    }
	    return (0, dxJsonParser_1.parseDx)((0, sdaxArchive_1.readEntryAsText)(entry));
	}
	function parseDcfFromSdax(extracted, designName) {
	    const candidates = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.dcf');
	    if (candidates.length === 0) {
	        return null;
	    }
	    let selectedPath = null;
	    if (designName.length > 0) {
	        const wantedSuffix = `${designName.toLowerCase()}.dcf`;
	        for (const path of candidates) {
	            if (path.toLowerCase().endsWith(wantedSuffix)) {
	                selectedPath = path;
	                break;
	            }
	        }
	    }
	    selectedPath ??= candidates[0];
	    const entry = extracted.entries.get(selectedPath);
	    if (entry === undefined) {
	        return null;
	    }
	    return (0, dcfParser_1.parseDcf)(entry.contents);
	}
	function decodeMasterFromSdax(extracted, designName) {
	    const allAscii = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.ascii');
	    const pagePathPattern = /page_file_\d+\.ascii$/i;
	    let masterPath = null;
	    if (designName.length > 0) {
	        const wantedSuffix = `/${designName.toLowerCase()}.ascii`;
	        for (const path of allAscii) {
	            if (path.toLowerCase().endsWith(wantedSuffix) && !pagePathPattern.test(path)) {
	                masterPath = path;
	                break;
	            }
	        }
	    }
	    if (masterPath === null) {
	        const pageDirs = new Set();
	        for (const path of allAscii) {
	            if (pagePathPattern.test(path)) {
	                const idx = path.lastIndexOf('/');
	                pageDirs.add(idx >= 0 ? path.slice(0, idx) : '');
	            }
	        }
	        for (const path of allAscii) {
	            if (pagePathPattern.test(path) || path.toLowerCase().startsWith('cache/')) {
	                continue;
	            }
	            const idx = path.lastIndexOf('/');
	            const parent = idx >= 0 ? path.slice(0, idx) : '';
	            if (pageDirs.has(parent)) {
	                masterPath = path;
	                break;
	            }
	        }
	    }
	    if (masterPath === null) {
	        return null;
	    }
	    const entry = extracted.entries.get(masterPath);
	    if (entry === undefined) {
	        return null;
	    }
	    const text = (0, sdaxArchive_1.readEntryAsText)(entry);
	    const doc = (0, decode_document_1.decodeDocument)(new parser_1.BracketParser(text).parse());
	    if (doc.body.kind !== 'master') {
	        return null;
	    }
	    return { doc, body: doc.body };
	}
	function decodeSheetPagesFromSdax(extracted, masterBundle, allowFilenameFallback) {
	    const pagePathPattern = /page_file_(\d+)\.ascii$/i;
	    const allAscii = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.ascii').filter((p) => pagePathPattern.test(p));
	    const pageByBasename = new Map();
	    for (const path of allAscii) {
	        const basename = stripPageBasename(path);
	        if (basename !== null) {
	            pageByBasename.set(basename, path);
	        }
	    }
	    const orderedRefs = [];
	    if (masterBundle !== null && masterBundle.body.pageRefs.length > 0) {
	        for (const ref of masterBundle.body.pageRefs) {
	            const sourcePath = pageByBasename.get(ref.pageFile);
	            if (sourcePath === undefined) {
	                continue;
	            }
	            const fileNumMatch = /page_file_(\d+)/i.exec(ref.pageFile);
	            const filePageNum = fileNumMatch === null ? '' : fileNumMatch[1];
	            const sheetIdMatch = /\s*\((\d+)\)$/.exec(ref.pageName);
	            const cleanedSheetName = sheetIdMatch === null ? ref.pageName : ref.pageName.slice(0, sheetIdMatch.index).trim();
	            const sheetId = sheetIdMatch === null ? parseInt(ref.pageNum, 10) || 0 : parseInt(sheetIdMatch[1], 10);
	            orderedRefs.push({
	                sourcePath,
	                pageFileName: ref.pageFile,
	                pageNum: filePageNum,
	                sheetName: cleanedSheetName,
	                sheetId,
	                pageType: 1,
	            });
	        }
	    }
	    else if (allowFilenameFallback) {
	        const sorted = [...allAscii].sort((a, b) => sheetPageIndex(a, pagePathPattern) - sheetPageIndex(b, pagePathPattern));
	        for (const sourcePath of sorted) {
	            const basename = stripPageBasename(sourcePath) ?? '';
	            const numMatch = pagePathPattern.exec(sourcePath);
	            const pageNum = numMatch === null ? '' : numMatch[1];
	            orderedRefs.push({
	                sourcePath,
	                pageFileName: basename,
	                pageNum,
	                sheetName: '',
	                sheetId: 0,
	                pageType: 0,
	            });
	        }
	    }
	    const instanceHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildInstanceHashMap)(masterBundle.body);
	    const netHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildNetHashMap)(masterBundle.body);
	    const pages = [];
	    for (const ref of orderedRefs) {
	        const entry = extracted.entries.get(ref.sourcePath);
	        if (entry === undefined) {
	            continue;
	        }
	        const text = (0, sdaxArchive_1.readEntryAsText)(entry);
	        const doc = (0, decode_document_1.decodeDocument)(new parser_1.BracketParser(text).parse(), {
	            instanceHashMap,
	            netHashMap,
	            pageNum: ref.pageNum,
	        });
	        if (doc.body.kind !== 'page') {
	            continue;
	        }
	        pages.push({
	            sourcePath: ref.sourcePath,
	            pageFileName: ref.pageFileName,
	            pageNum: ref.pageNum,
	            sheetName: ref.sheetName,
	            sheetId: ref.sheetId,
	            pageType: ref.pageType,
	            body: doc.body,
	            decoded: doc,
	        });
	    }
	    return pages;
	}
	function stripPageBasename(path) {
	    const match = /(page_file_\d+)\.ascii$/i.exec(path);
	    return match === null ? null : match[1];
	}
	function sheetPageIndex(path, pattern) {
	    const match = pattern.exec(path);
	    if (match === null) {
	        return 0;
	    }
	    const n = parseInt(match[1], 10);
	    return Number.isNaN(n) ? 0 : n;
	}
	function attachParseResults(projectModel, masterBundle, sheetPages, packagerNetlist, packagerPartList, styleWarnings) {
	    const allWarnings = [];
	    if (masterBundle !== null) {
	        allWarnings.push(...masterBundle.doc.warnings);
	    }
	    for (const page of sheetPages) {
	        allWarnings.push(...page.decoded.warnings);
	    }
	    if (allWarnings.length > 0) {
	        const decodeResult = {
	            type: 'allegro-decode',
	            message: `Allegro ASCII decode completed with ${allWarnings.length} diagnostic(s)`,
	            innerResults: allWarnings,
	        };
	        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(decodeResult);
	        projectModel.processingResults.innerResults.push(decodeResult);
	    }
	    if (packagerNetlist !== undefined && packagerNetlist.processingResults.innerResults.length > 0) {
	        projectModel.processingResults.innerResults.push(packagerNetlist.processingResults);
	    }
	    if (packagerPartList !== undefined && packagerPartList.processingResults.innerResults.length > 0) {
	        projectModel.processingResults.innerResults.push(packagerPartList.processingResults);
	    }
	    if (styleWarnings.length > 0) {
	        const styleResult = {
	            type: 'allegro-style',
	            message: `Allegro style parsing completed with ${styleWarnings.length} diagnostic(s)`,
	            innerResults: styleWarnings,
	        };
	        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(styleResult);
	        projectModel.processingResults.innerResults.push(styleResult);
	    }
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(projectModel.processingResults);
	}
	function buildImageResolver(extracted) {
	    if (extracted === null) {
	        return () => null;
	    }
	    const byBasename = new Map();
	    for (const [path, entry] of extracted.entries) {
	        const slash = path.lastIndexOf('/');
	        const base = slash >= 0 ? path.slice(slash + 1) : path;
	        if (!byBasename.has(base)) {
	            byBasename.set(base, entry.contents);
	        }
	    }
	    return (filename) => byBasename.get(filename) ?? null;
	}
	
	return parseAllegroProject;
}

var allegroNetlist = {};

var allegroNetlistToSchematic = {};

var hasRequiredAllegroNetlistToSchematic;

function requireAllegroNetlistToSchematic () {
	if (hasRequiredAllegroNetlistToSchematic) return allegroNetlistToSchematic;
	hasRequiredAllegroNetlistToSchematic = 1;
	Object.defineProperty(allegroNetlistToSchematic, "__esModule", { value: true });
	allegroNetlistToSchematic.convertAllegroNetlistToSchematic = convertAllegroNetlistToSchematic;
	allegroNetlistToSchematic.getSchematicFromAllegroParseResult = getSchematicFromAllegroParseResult;
	const sigma_engine_shared_1 = requireDist$1();
	const AllegroTypes_1 = requireAllegroTypes();
	const AllegroUnconnectedPinNamingRule_1 = requireAllegroUnconnectedPinNamingRule();
	function convertAllegroNetlistToSchematic(netlist, partList, designId = 'allegro-netlist') {
	    const innerResults = [];
	    const partLookup = new Map();
	    if (partList) {
	        for (const part of partList.parts) {
	            partLookup.set(part.refdes, { partName: part.partName, properties: part.properties });
	        }
	    }
	    const moduleMap = new Map();
	    for (const net of netlist.nets) {
	        for (const node of net.nodes) {
	            if (!moduleMap.has(node.refdes)) {
	                const partInfo = partLookup.get(node.refdes);
	                const transform = {
	                    angle: 0,
	                    mirrored: [false, false],
	                };
	                const module = {
	                    ref: node.refdes,
	                    footprintRef: partInfo?.properties['PACK_TYPE'] ?? partInfo?.partName ?? node.refdes,
	                    componentRef: partInfo?.partName ?? node.refdes,
	                    center: [0, 0],
	                    layer: 'F',
	                    transform,
	                    name: node.refdes,
	                    pinNets: {},
	                    drawProperties: {},
	                };
	                if (partInfo?.properties['VALUE']) {
	                    module.drawProperties['value'] = {
	                        value: partInfo.properties['VALUE'],
	                        hidden: true,
	                    };
	                }
	                moduleMap.set(node.refdes, module);
	            }
	            const module = moduleMap.get(node.refdes);
	            module.pinNets[node.pin] = net.name === 'NC' ? (0, AllegroUnconnectedPinNamingRule_1.unconnectedPinNetName)(node.refdes, node.pin) : net.name;
	        }
	    }
	    const modules = Array.from(moduleMap.values());
	    const nets = {};
	    for (const net of netlist.nets) {
	        if (net.name === 'NC') {
	            for (const node of net.nodes) {
	                const syntheticName = (0, AllegroUnconnectedPinNamingRule_1.unconnectedPinNetName)(node.refdes, node.pin);
	                nets[syntheticName] = {
	                    name: syntheticName,
	                    connections: [
	                        {
	                            moduleRef: node.refdes,
	                            moduleName: node.refdes,
	                            pinRef: node.pin,
	                        },
	                    ],
	                };
	            }
	            continue;
	        }
	        const connections = [];
	        for (const node of net.nodes) {
	            connections.push({
	                moduleRef: node.refdes,
	                moduleName: node.refdes,
	                pinRef: node.pin,
	            });
	        }
	        if (nets[net.name]) {
	            innerResults.push({
	                type: AllegroTypes_1.AllegroNetlistConversionMessageType.DuplicateNet,
	                message: `Duplicate net name "${net.name}" — merging connections`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	            });
	            nets[net.name].connections.push(...connections);
	        }
	        else {
	            nets[net.name] = {
	                name: net.name,
	                connections,
	            };
	        }
	    }
	    let title = 'Allegro Netlist Import';
	    if (partList?.directives['ROOT_DRAWING']) {
	        title = partList.directives['ROOT_DRAWING'];
	    }
	    const schematic = {
	        modelType: 'schematic',
	        designId,
	        sourceFile: 'allegro-netlist',
	        sheetIndex: 0,
	        layerSet: 'default',
	        drawings: {},
	        edges_bbox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
	        initial_zoombox: { minx: 0, miny: 0, maxx: 0, maxy: 0 },
	        nets,
	        modules,
	        ports: {},
	        metadata: {
	            title,
	            revision: '',
	            company: '',
	            date: partList?.directives['POST_TIME'] ?? new Date().toISOString().split('T')[0],
	        },
	        warnings: [],
	    };
	    const processingResults = {
	        type: 'allegro-netlist-conversion',
	        message: `Allegro netlist conversion completed with ${innerResults.length} diagnostic(s)`,
	        innerResults,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(processingResults);
	    return { schematic, processingResults };
	}
	function getSchematicFromAllegroParseResult(parseResult, designId) {
	    if (parseResult.processingResults.severity === sigma_engine_shared_1.BoundaryLevel.Error) {
	        return undefined;
	    }
	    if (!parseResult.netlist.nets.length) {
	        return undefined;
	    }
	    return convertAllegroNetlistToSchematic(parseResult.netlist, parseResult.partList, designId);
	}
	
	return allegroNetlistToSchematic;
}

var hasRequiredAllegroNetlist;

function requireAllegroNetlist () {
	if (hasRequiredAllegroNetlist) return allegroNetlist;
	hasRequiredAllegroNetlist = 1;
	(function (exports) {
		var __createBinding = (allegroNetlist && allegroNetlist.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (allegroNetlist && allegroNetlist.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAllegroTypes(), exports);
		__exportStar(requireAllegroNetlistParser(), exports);
		__exportStar(requireAllegroPartListParser(), exports);
		__exportStar(requireAllegroNetlistToSchematic(), exports);
		
	} (allegroNetlist));
	return allegroNetlist;
}

var ascii = {};

var bracket = {};

var hasRequiredBracket;

function requireBracket () {
	if (hasRequiredBracket) return bracket;
	hasRequiredBracket = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.BracketParser = void 0;
		var parser_1 = requireParser();
		Object.defineProperty(exports, "BracketParser", { enumerable: true, get: function () { return parser_1.BracketParser; } });
		
	} (bracket));
	return bracket;
}

var decode = {};

var cursor = {};

var hasRequiredCursor;

function requireCursor () {
	if (hasRequiredCursor) return cursor;
	hasRequiredCursor = 1;
	Object.defineProperty(cursor, "__esModule", { value: true });
	cursor.ChildCursor = void 0;
	class ChildCursor {
	    constructor(nodes) {
	        this.nodes = nodes;
	        this.pos = 0;
	    }
	    get done() {
	        return this.pos >= this.nodes.length;
	    }
	    get index() {
	        return this.pos;
	    }
	    nodeAt(index) {
	        return this.nodes[index];
	    }
	    peekType() {
	        if (this.done) {
	            return null;
	        }
	        return this.nodes[this.pos].type;
	    }
	    consumeAtoms() {
	        const atoms = [];
	        while (!this.done && this.nodes[this.pos].type === 'atom') {
	            const node = this.nodes[this.pos];
	            if (node.type === 'atom') {
	                atoms.push(node.content);
	            }
	            this.pos++;
	        }
	        return atoms;
	    }
	    takePayload() {
	        if (this.done) {
	            throw new Error('ChildCursor: takePayload called past end of child list');
	        }
	        const node = this.nodes[this.pos];
	        if (node.type === 'atom') {
	            throw new Error(`ChildCursor: takePayload called when next node is an atom ("${node.content}") — call consumeAtoms() first`);
	        }
	        const index = this.pos;
	        this.pos++;
	        return { node: node, index };
	    }
	    takeContainer() {
	        if (this.done) {
	            return null;
	        }
	        const node = this.nodes[this.pos];
	        if (node.type !== 'container' && node.type !== 'econtainer') {
	            return null;
	        }
	        const index = this.pos;
	        this.pos++;
	        return { node, index };
	    }
	    takeName() {
	        if (this.done) {
	            return null;
	        }
	        const node = this.nodes[this.pos];
	        if (node.type !== 'name') {
	            return null;
	        }
	        const index = this.pos;
	        this.pos++;
	        return { node, index };
	    }
	    takeValue() {
	        if (this.done) {
	            return null;
	        }
	        const node = this.nodes[this.pos];
	        if (node.type !== 'value') {
	            return null;
	        }
	        const index = this.pos;
	        this.pos++;
	        return { node, index };
	    }
	}
	cursor.ChildCursor = ChildCursor;
	
	return cursor;
}

var scanner = {};

var hasRequiredScanner;

function requireScanner () {
	if (hasRequiredScanner) return scanner;
	hasRequiredScanner = 1;
	Object.defineProperty(scanner, "__esModule", { value: true });
	scanner.scanChildren = scanChildren;
	function scanChildren(nodes) {
	    const groups = [];
	    let atomBuffer = [];
	    let atomStartIndex = 0;
	    for (let i = 0; i < nodes.length; i++) {
	        const node = nodes[i];
	        if (node.type === 'atom') {
	            if (atomBuffer.length === 0) {
	                atomStartIndex = i;
	            }
	            atomBuffer.push(node.content);
	        }
	        else {
	            groups.push({
	                atoms: atomBuffer,
	                atomStartIndex: atomBuffer.length === 0 ? i : atomStartIndex,
	                node: node,
	                payloadIndex: i,
	            });
	            atomBuffer = [];
	        }
	    }
	    return {
	        groups,
	        trailingAtoms: atomBuffer,
	        trailingStartIndex: atomBuffer.length > 0 ? atomStartIndex : -1,
	    };
	}
	
	return scanner;
}

var decoders = {};

var hasRequiredDecoders;

function requireDecoders () {
	if (hasRequiredDecoders) return decoders;
	hasRequiredDecoders = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.decodeGraphicsAnnotation = exports.readCoordsContainer = exports.readCadencePosition = exports.decodeVisualLabel = exports.decodeTerminal = exports.decodeComponentInstance = exports.decodePowerSymbol = exports.decodePageMetadata = exports.decodePageInstance = exports.decodePageBorder = exports.decodeWireSegment = exports.decodeWireBundle = exports.decodeNetStub = exports.isPlaceholderContainer = exports.entityListItems = exports.detectEntityList = exports.decodeEntityList = exports.findPropValue = exports.findProp = exports.decodePropBlock = exports.decodeMasterPageRefs = exports.buildNetHashSet = exports.buildNetHashMap = exports.buildInstanceHashSet = exports.buildInstanceHashMap = exports.decodeMasterDocument = exports.decodePageDocument = exports.decodeDocument = void 0;
		var decode_document_1 = requireDecodeDocument();
		Object.defineProperty(exports, "decodeDocument", { enumerable: true, get: function () { return decode_document_1.decodeDocument; } });
		var decode_page_document_1 = requireDecodePageDocument();
		Object.defineProperty(exports, "decodePageDocument", { enumerable: true, get: function () { return decode_page_document_1.decodePageDocument; } });
		var decode_master_document_1 = requireDecodeMasterDocument();
		Object.defineProperty(exports, "decodeMasterDocument", { enumerable: true, get: function () { return decode_master_document_1.decodeMasterDocument; } });
		Object.defineProperty(exports, "buildInstanceHashMap", { enumerable: true, get: function () { return decode_master_document_1.buildInstanceHashMap; } });
		Object.defineProperty(exports, "buildInstanceHashSet", { enumerable: true, get: function () { return decode_master_document_1.buildInstanceHashSet; } });
		Object.defineProperty(exports, "buildNetHashMap", { enumerable: true, get: function () { return decode_master_document_1.buildNetHashMap; } });
		Object.defineProperty(exports, "buildNetHashSet", { enumerable: true, get: function () { return decode_master_document_1.buildNetHashSet; } });
		var decode_master_page_ref_1 = requireDecodeMasterPageRef();
		Object.defineProperty(exports, "decodeMasterPageRefs", { enumerable: true, get: function () { return decode_master_page_ref_1.decodeMasterPageRefs; } });
		var decode_prop_block_1 = requireDecodePropBlock();
		Object.defineProperty(exports, "decodePropBlock", { enumerable: true, get: function () { return decode_prop_block_1.decodePropBlock; } });
		Object.defineProperty(exports, "findProp", { enumerable: true, get: function () { return decode_prop_block_1.findProp; } });
		Object.defineProperty(exports, "findPropValue", { enumerable: true, get: function () { return decode_prop_block_1.findPropValue; } });
		var entity_list_1 = requireEntityList();
		Object.defineProperty(exports, "decodeEntityList", { enumerable: true, get: function () { return entity_list_1.decodeEntityList; } });
		Object.defineProperty(exports, "detectEntityList", { enumerable: true, get: function () { return entity_list_1.detectEntityList; } });
		Object.defineProperty(exports, "entityListItems", { enumerable: true, get: function () { return entity_list_1.entityListItems; } });
		Object.defineProperty(exports, "isPlaceholderContainer", { enumerable: true, get: function () { return entity_list_1.isPlaceholderContainer; } });
		var decode_tc5_1 = requireDecodeTc5();
		Object.defineProperty(exports, "decodeNetStub", { enumerable: true, get: function () { return decode_tc5_1.decodeNetStub; } });
		var decode_tc8_wire_bundle_1 = requireDecodeTc8WireBundle();
		Object.defineProperty(exports, "decodeWireBundle", { enumerable: true, get: function () { return decode_tc8_wire_bundle_1.decodeWireBundle; } });
		var decode_tc11_wire_segment_1 = requireDecodeTc11WireSegment();
		Object.defineProperty(exports, "decodeWireSegment", { enumerable: true, get: function () { return decode_tc11_wire_segment_1.decodeWireSegment; } });
		var decode_tc6_tc14_tc55_1 = requireDecodeTc6Tc14Tc55();
		Object.defineProperty(exports, "decodePageBorder", { enumerable: true, get: function () { return decode_tc6_tc14_tc55_1.decodePageBorder; } });
		Object.defineProperty(exports, "decodePageInstance", { enumerable: true, get: function () { return decode_tc6_tc14_tc55_1.decodePageInstance; } });
		Object.defineProperty(exports, "decodePageMetadata", { enumerable: true, get: function () { return decode_tc6_tc14_tc55_1.decodePageMetadata; } });
		var decode_tc7_tc12_1 = requireDecodeTc7Tc12();
		Object.defineProperty(exports, "decodePowerSymbol", { enumerable: true, get: function () { return decode_tc7_tc12_1.decodePowerSymbol; } });
		Object.defineProperty(exports, "decodeComponentInstance", { enumerable: true, get: function () { return decode_tc7_tc12_1.decodeComponentInstance; } });
		var decode_tc13_terminal_1 = requireDecodeTc13Terminal();
		Object.defineProperty(exports, "decodeTerminal", { enumerable: true, get: function () { return decode_tc13_terminal_1.decodeTerminal; } });
		var decode_tc31_visual_label_1 = requireDecodeTc31VisualLabel();
		Object.defineProperty(exports, "decodeVisualLabel", { enumerable: true, get: function () { return decode_tc31_visual_label_1.decodeVisualLabel; } });
		Object.defineProperty(exports, "readCadencePosition", { enumerable: true, get: function () { return decode_tc31_visual_label_1.readCadencePosition; } });
		Object.defineProperty(exports, "readCoordsContainer", { enumerable: true, get: function () { return decode_tc31_visual_label_1.readCoordsContainer; } });
		var decode_graphics_annotation_1 = requireDecodeGraphicsAnnotation();
		Object.defineProperty(exports, "decodeGraphicsAnnotation", { enumerable: true, get: function () { return decode_graphics_annotation_1.decodeGraphicsAnnotation; } });
		
	} (decoders));
	return decoders;
}

var hasRequiredDecode;

function requireDecode () {
	if (hasRequiredDecode) return decode;
	hasRequiredDecode = 1;
	(function (exports) {
		var __createBinding = (decode && decode.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (decode && decode.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.getNamedValueDeep = exports.getNamedValue = exports.extractBBox = exports.findCoordsContainer = exports.findPrefixedDeep = exports.findPrefixed = exports.childrenOf = exports.atomContent = exports.scanChildren = exports.ChildCursor = void 0;
		var cursor_1 = requireCursor();
		Object.defineProperty(exports, "ChildCursor", { enumerable: true, get: function () { return cursor_1.ChildCursor; } });
		var scanner_1 = requireScanner();
		Object.defineProperty(exports, "scanChildren", { enumerable: true, get: function () { return scanner_1.scanChildren; } });
		var helpers_1 = requireHelpers();
		Object.defineProperty(exports, "atomContent", { enumerable: true, get: function () { return helpers_1.atomContent; } });
		Object.defineProperty(exports, "childrenOf", { enumerable: true, get: function () { return helpers_1.childrenOf; } });
		Object.defineProperty(exports, "findPrefixed", { enumerable: true, get: function () { return helpers_1.findPrefixed; } });
		Object.defineProperty(exports, "findPrefixedDeep", { enumerable: true, get: function () { return helpers_1.findPrefixedDeep; } });
		Object.defineProperty(exports, "findCoordsContainer", { enumerable: true, get: function () { return helpers_1.findCoordsContainer; } });
		Object.defineProperty(exports, "extractBBox", { enumerable: true, get: function () { return helpers_1.extractBBox; } });
		Object.defineProperty(exports, "getNamedValue", { enumerable: true, get: function () { return helpers_1.getNamedValue; } });
		Object.defineProperty(exports, "getNamedValueDeep", { enumerable: true, get: function () { return helpers_1.getNamedValueDeep; } });
		__exportStar(requireDecodedTypes(), exports);
		__exportStar(requireDecoders(), exports);
		
	} (decode));
	return decode;
}

var hasRequiredAscii;

function requireAscii () {
	if (hasRequiredAscii) return ascii;
	hasRequiredAscii = 1;
	(function (exports) {
		var __createBinding = (ascii && ascii.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ascii && ascii.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBracket(), exports);
		__exportStar(requireDecode(), exports);
		
	} (ascii));
	return ascii;
}

var hasRequiredAllegro;

function requireAllegro () {
	if (hasRequiredAllegro) return Allegro;
	hasRequiredAllegro = 1;
	(function (exports) {
		var __createBinding = (Allegro && Allegro.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Allegro && Allegro.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DecodeWarningType = exports.buildNetHashSet = exports.buildNetHashMap = exports.buildInstanceHashSet = exports.buildInstanceHashMap = exports.decodeMasterDocument = exports.decodePageDocument = exports.decodeDocument = exports.BracketParser = void 0;
		__exportStar(requireParseAllegroProject(), exports);
		__exportStar(requireAllegroValidation(), exports);
		__exportStar(requireAllegroNetlist(), exports);
		__exportStar(requireCdszArchive(), exports);
		var ascii_1 = requireAscii();
		Object.defineProperty(exports, "BracketParser", { enumerable: true, get: function () { return ascii_1.BracketParser; } });
		var ascii_2 = requireAscii();
		Object.defineProperty(exports, "decodeDocument", { enumerable: true, get: function () { return ascii_2.decodeDocument; } });
		Object.defineProperty(exports, "decodePageDocument", { enumerable: true, get: function () { return ascii_2.decodePageDocument; } });
		Object.defineProperty(exports, "decodeMasterDocument", { enumerable: true, get: function () { return ascii_2.decodeMasterDocument; } });
		Object.defineProperty(exports, "buildInstanceHashMap", { enumerable: true, get: function () { return ascii_2.buildInstanceHashMap; } });
		Object.defineProperty(exports, "buildInstanceHashSet", { enumerable: true, get: function () { return ascii_2.buildInstanceHashSet; } });
		Object.defineProperty(exports, "buildNetHashMap", { enumerable: true, get: function () { return ascii_2.buildNetHashMap; } });
		Object.defineProperty(exports, "buildNetHashSet", { enumerable: true, get: function () { return ascii_2.buildNetHashSet; } });
		Object.defineProperty(exports, "DecodeWarningType", { enumerable: true, get: function () { return ascii_2.DecodeWarningType; } });
		
	} (Allegro));
	return Allegro;
}

var dru = {};

var hasRequiredDru;

function requireDru () {
	if (hasRequiredDru) return dru;
	hasRequiredDru = 1;
	Object.defineProperty(dru, "__esModule", { value: true });
	dru.DesignRuleSchema = void 0;
	dru.parseDru = parseDru;
	const lengthAttributes = [
	    'mdWireWire',
	    'mdWirePad',
	    'mdWireVia',
	    'mdPadPad',
	    'mdPadVia',
	    'mdViaVia',
	    'mdSmdPad',
	    'mdSmdVia',
	    'mdSmdSmd',
	    'mdViaViaSameLayer',
	    'mdCopperDimension',
	    'mdDrill',
	    'mdSmdStop',
	    'msWidth',
	    'msDrill',
	    'msMicroVia',
	    'rlMinPadTop',
	    'rlMaxPadTop',
	    'rlMinPadInner',
	    'rlMaxPadInner',
	    'rlMinPadBottom',
	    'rlMaxPadBottom',
	    'rlMinViaOuter',
	    'rlMaxViaOuter',
	    'rlMinViaInner',
	    'rlMaxViaInner',
	    'rlMinMicroViaOuter',
	    'rlMaxMicroViaOuter',
	    'rlMinMicroViaInner',
	    'rlMaxMicroViaInner',
	    'mlMinStopFrame',
	    'mlMaxStopFrame',
	    'mlMinCreamFrame',
	    'mlMaxCreamFrame',
	    'mlViaStopLimit',
	    'srMinRoundness',
	    'srMaxRoundness',
	    'slMinThermalGap',
	    'slMaxThermalGap',
	    'slAnnulusIsolate',
	    'slThermalIsolate',
	];
	function parseDru(dru) {
	    const result = {};
	    const lines = dru.split('\n');
	    lines.forEach((line) => {
	        const [name, value] = line
	            .split('=')
	            .map((v) => v.trim())
	            .filter((v) => v.length);
	        if ('layerSetup' === name) {
	            result.layerSetup = value;
	        }
	        else if ('mtCopper' === name) {
	            result.mtCopper = value.split(' ').map((v) => formatMeasurement(v));
	        }
	        else if ('mtIsolate' === name) {
	            result.mtIsolate = value.split(' ').map((v) => formatMeasurement(v));
	        }
	        else if (lengthAttributes.includes(name)) {
	            result[name] = formatMeasurement(value);
	        }
	        else {
	            result[name] = parseFloat(value);
	        }
	    });
	    return result;
	}
	function formatMeasurement(value) {
	    if (value.endsWith('mil')) {
	        return parseFloat(value.substr(0, value.length - 3)) / 39.37;
	    }
	    return parseFloat(value.substr(0, value.length - 2));
	}
	class DesignRuleSchema {
	}
	dru.DesignRuleSchema = DesignRuleSchema;
	
	return dru;
}

var ModelValidation = {};

var validateProjectModel = {};

var hasRequiredValidateProjectModel;

function requireValidateProjectModel () {
	if (hasRequiredValidateProjectModel) return validateProjectModel;
	hasRequiredValidateProjectModel = 1;
	Object.defineProperty(validateProjectModel, "__esModule", { value: true });
	validateProjectModel.ProjectModelValidationErrorType = void 0;
	validateProjectModel.validateProjectModel = validateProjectModel$1;
	const sigma_engine_shared_1 = requireDist$1();
	var ProjectModelValidationErrorType;
	(function (ProjectModelValidationErrorType) {
	    ProjectModelValidationErrorType["DuplicateProjectModuleName"] = "duplicate-project-module-name";
	    ProjectModelValidationErrorType["InvalidComponentReference"] = "invalid-component-reference";
	    ProjectModelValidationErrorType["InvalidFootprintReference"] = "invalid-footprint-reference";
	    ProjectModelValidationErrorType["InvalidInstanceDesignId"] = "invalid-instance-design-id";
	    ProjectModelValidationErrorType["InvalidInstanceModuleRef"] = "invalid-instance-module-ref";
	    ProjectModelValidationErrorType["DuplicateSchematicModuleRef"] = "duplicate-schematic-module-ref";
	    ProjectModelValidationErrorType["InvalidSchematicComponentReference"] = "invalid-schematic-component-reference";
	    ProjectModelValidationErrorType["InvalidSchematicFootprintReference"] = "invalid-schematic-footprint-reference";
	    ProjectModelValidationErrorType["InvalidPinReference"] = "invalid-pin-reference";
	    ProjectModelValidationErrorType["InvalidProjectModuleLinkage"] = "invalid-project-module-linkage";
	    ProjectModelValidationErrorType["NetNameMismatch"] = "net-name-mismatch";
	    ProjectModelValidationErrorType["InvalidNetModuleReference"] = "invalid-net-module-reference";
	    ProjectModelValidationErrorType["InvalidNetPinReference"] = "invalid-net-pin-reference";
	    ProjectModelValidationErrorType["InvalidConnectionModuleName"] = "invalid-connection-module-name";
	    ProjectModelValidationErrorType["DuplicateNetConnection"] = "duplicate-net-connection";
	    ProjectModelValidationErrorType["EmptyNet"] = "empty-net";
	    ProjectModelValidationErrorType["SinglePinNet"] = "single-pin-net";
	    ProjectModelValidationErrorType["DrawingOnlyNet"] = "drawing-only-net";
	    ProjectModelValidationErrorType["OrphanedPinConnection"] = "orphaned-pin-connection";
	    ProjectModelValidationErrorType["InvalidPinNetReference"] = "invalid-pin-net-reference";
	    ProjectModelValidationErrorType["MissingFootprints"] = "missing-footprints";
	    ProjectModelValidationErrorType["MissingSymbols"] = "missing-symbols";
	    ProjectModelValidationErrorType["PinCountMismatch"] = "pin-count-mismatch";
	    ProjectModelValidationErrorType["PadCountMismatch"] = "pad-count-mismatch";
	    ProjectModelValidationErrorType["InvalidSignalPinReference"] = "invalid-signal-pin-reference";
	    ProjectModelValidationErrorType["InvalidSignalPadReference"] = "invalid-signal-pad-reference";
	    ProjectModelValidationErrorType["DuplicatePinInSignals"] = "duplicate-pin-in-signals";
	    ProjectModelValidationErrorType["DuplicateInstanceName"] = "duplicate-instance-name";
	    ProjectModelValidationErrorType["InvalidInstanceSheetIndex"] = "invalid-instance-sheet-index";
	    ProjectModelValidationErrorType["MultiInstanceMismatch"] = "multi-instance-mismatch";
	    ProjectModelValidationErrorType["UnusedComponent"] = "unused-component";
	    ProjectModelValidationErrorType["UnusedFootprint"] = "unused-footprint";
	    ProjectModelValidationErrorType["MissingMetadata"] = "missing-metadata";
	    ProjectModelValidationErrorType["InconsistentNetConnection"] = "InconsistentNetConnection";
	})(ProjectModelValidationErrorType || (validateProjectModel.ProjectModelValidationErrorType = ProjectModelValidationErrorType = {}));
	const DEFAULT_OPTIONS = {
	    emptyNetSeverity: undefined,
	    singlePinNetSeverity: undefined,
	    drawingOnlyNetSeverity: undefined,
	    unusedComponentSeverity: sigma_engine_shared_1.BoundaryLevel.Warning,
	    unusedFootprintSeverity: undefined,
	    missingFootprintsSeverity: sigma_engine_shared_1.BoundaryLevel.Warning,
	    missingSymbolsSeverity: sigma_engine_shared_1.BoundaryLevel.Warning,
	    multiInstanceMismatchSeverity: sigma_engine_shared_1.BoundaryLevel.Error,
	    strictPinValidation: true,
	};
	function validateProjectModel$1(projectModel, options = {}) {
	    const opts = { ...DEFAULT_OPTIONS, ...options };
	    const innerResults = [];
	    innerResults.push(validateProjectModules(projectModel));
	    for (const [designId, design] of Object.entries(projectModel.designs)) {
	        if (design.modelType === 'schematic') {
	            innerResults.push(validateSchematic(projectModel, design, designId, opts));
	        }
	    }
	    innerResults.push(validateComponents(projectModel, opts));
	    innerResults.push(validateStructuralIssues(projectModel, opts));
	    const result = {
	        type: 'project-model-validation',
	        message: 'Project model validation complete',
	        innerResults,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function validateProjectModules(projectModel, _opts) {
	    const innerResults = [];
	    const moduleNames = new Set();
	    const duplicateNames = new Set();
	    for (const module of projectModel.modules) {
	        if (moduleNames.has(module.name)) {
	            duplicateNames.add(module.name);
	        }
	        moduleNames.add(module.name);
	    }
	    if (duplicateNames.size > 0) {
	        innerResults.push({
	            type: ProjectModelValidationErrorType.DuplicateProjectModuleName,
	            message: `Duplicate project module names found: ${Array.from(duplicateNames).join(', ')}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            duplicateNames: Array.from(duplicateNames),
	        });
	    }
	    for (const module of projectModel.modules) {
	        if (!projectModel.components[module.componentRef]) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.InvalidComponentReference,
	                message: `Project module "${module.name}" references non-existent component "${module.componentRef}"`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                moduleName: module.name,
	                componentRef: module.componentRef,
	            });
	        }
	    }
	    for (const module of projectModel.modules) {
	        for (const instance of module.instances) {
	            if (!projectModel.designs[instance.designId]) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InvalidInstanceDesignId,
	                    message: `Project module "${module.name}" instance references non-existent design "${instance.designId}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    moduleName: module.name,
	                    designId: instance.designId,
	                });
	                continue;
	            }
	            const design = projectModel.designs[instance.designId];
	            if (design.modelType === 'schematic') {
	                const moduleExists = design.modules.some((m) => m.ref === instance.ref);
	                if (!moduleExists) {
	                    innerResults.push({
	                        type: ProjectModelValidationErrorType.InvalidInstanceModuleRef,
	                        message: `Project module "${module.name}" instance references non-existent module ref "${instance.ref}" in design "${instance.designId}"`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                        moduleName: module.name,
	                        designId: instance.designId,
	                        moduleRef: instance.ref,
	                    });
	                }
	            }
	        }
	    }
	    const result = {
	        type: 'project-module-validation',
	        message: innerResults.length === 0 ? 'All project modules are valid' : 'Project module validation found issues',
	        innerResults,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function validateSchematic(projectModel, schematic, designId, opts) {
	    const innerResults = [];
	    innerResults.push(validateSchematicModules(projectModel, schematic, designId, opts));
	    innerResults.push(validateNets(projectModel, schematic, designId, opts));
	    if (schematic.multiInstanceConfiguration) {
	        innerResults.push(validateMultiInstanceConfiguration(schematic, designId));
	    }
	    const result = {
	        type: 'schematic-validation',
	        message: `Schematic "${designId}" validation`,
	        innerResults,
	        designId,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function validateSchematicModules(projectModel, schematic, designId, opts) {
	    const innerResults = [];
	    const moduleRefs = new Set();
	    const duplicateRefs = new Set();
	    for (const module of schematic.modules) {
	        if (moduleRefs.has(module.ref)) {
	            duplicateRefs.add(module.ref);
	        }
	        moduleRefs.add(module.ref);
	    }
	    if (duplicateRefs.size > 0) {
	        innerResults.push({
	            type: ProjectModelValidationErrorType.DuplicateSchematicModuleRef,
	            message: `Duplicate module REFs in schematic "${designId}": ${Array.from(duplicateRefs).join(', ')}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            designId,
	            duplicateRefs: Array.from(duplicateRefs),
	        });
	    }
	    for (const module of schematic.modules) {
	        if (!projectModel.components[module.componentRef]) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.InvalidSchematicComponentReference,
	                message: `Module "${module.ref}" in schematic "${designId}" references non-existent component "${module.componentRef}"`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                designId,
	                moduleRef: module.ref,
	                componentRef: module.componentRef,
	            });
	            continue;
	        }
	        const component = projectModel.components[module.componentRef];
	        const isSchematic = schematic.modelType === 'schematic';
	        const collectionName = isSchematic ? 'symbol' : 'footprint';
	        const footprintExists = isSchematic ?
	            component.symbols.some((fp) => fp.ref === module.footprintRef)
	            : component.footprints.some((fp) => fp.ref === module.footprintRef);
	        if (!footprintExists) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.InvalidSchematicFootprintReference,
	                message: `Module "${module.ref}" in ${schematic.modelType} "${designId}" references non-existent ${collectionName} "${module.footprintRef}" in component "${module.componentRef}"`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                designId,
	                moduleRef: module.ref,
	                componentRef: module.componentRef,
	                footprintRef: module.footprintRef,
	            });
	        }
	        if (opts.strictPinValidation) {
	            const allPins = new Set();
	            for (const symbol of component.symbols) {
	                for (const pin of symbol.pins) {
	                    allPins.add(pin.ref);
	                }
	            }
	            for (const pinRef of Object.keys(module.pinNets)) {
	                if (!allPins.has(pinRef)) {
	                    innerResults.push({
	                        type: ProjectModelValidationErrorType.InvalidPinReference,
	                        message: `Module "${module.ref}" in schematic "${designId}" references non-existent pin "${pinRef}" in component "${module.componentRef}"`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                        designId,
	                        moduleRef: module.ref,
	                        componentRef: module.componentRef,
	                        pinRef,
	                    });
	                }
	            }
	        }
	        if (!(component.isVirtual && module.componentRef.startsWith('schref_'))) {
	            const lookupName = module.fullModuleName ?? module.name;
	            const projectModule = projectModel.modules.find((pm) => pm.name === lookupName);
	            if (!projectModule) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InvalidProjectModuleLinkage,
	                    message: `Module "${module.ref}" in schematic "${designId}" does not link to any project module (lookup name: "${lookupName}")`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    designId,
	                    moduleRef: module.ref,
	                    moduleName: module.name,
	                    fullModuleName: module.fullModuleName,
	                    lookupName,
	                });
	            }
	        }
	        if (opts.multiInstanceMismatchSeverity) {
	            const hasSchematicMultiInstance = !!schematic.multiInstanceConfiguration;
	            const hasModuleMultiInstance = !!module.multiInstance;
	            if (hasSchematicMultiInstance !== hasModuleMultiInstance) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.MultiInstanceMismatch,
	                    message: `Module "${module.ref}" in schematic "${designId}" has multi-instance mismatch: schematic has ${hasSchematicMultiInstance ? 'multi-instance' : 'no multi-instance'} but module has ${hasModuleMultiInstance ? 'multi-instance' : 'no multi-instance'}`,
	                    severity: opts.multiInstanceMismatchSeverity,
	                    designId,
	                    moduleRef: module.ref,
	                    hasSchematicMultiInstance,
	                    hasModuleMultiInstance,
	                });
	            }
	        }
	    }
	    const result = {
	        type: 'schematic-module-validation',
	        message: innerResults.length === 0 ?
	            `All modules in schematic "${designId}" are valid`
	            : `Module validation found issues in schematic "${designId}"`,
	        innerResults,
	        designId,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function validateNets(projectModel, schematic, designId, opts) {
	    const innerResults = [];
	    const moduleMap = new Map();
	    for (const module of schematic.modules) {
	        moduleMap.set(module.ref, module);
	    }
	    const componentPinMap = new Map();
	    for (const module of schematic.modules) {
	        if (!componentPinMap.has(module.componentRef)) {
	            const component = projectModel.components[module.componentRef];
	            if (component) {
	                const pins = new Set();
	                for (const symbol of component.symbols) {
	                    for (const pin of symbol.pins) {
	                        pins.add(pin.ref);
	                    }
	                }
	                componentPinMap.set(module.componentRef, pins);
	            }
	        }
	    }
	    for (const [netName, net] of Object.entries(schematic.nets)) {
	        if (net.name !== netName) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.NetNameMismatch,
	                message: `Net key "${netName}" does not match net name "${net.name}" in schematic "${designId}"`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                designId,
	                netKey: netName,
	                netName: net.name,
	            });
	        }
	        if (opts.emptyNetSeverity && net.connections.length === 0) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.EmptyNet,
	                message: `Net "${netName}" in schematic "${designId}" has no connections`,
	                severity: opts.emptyNetSeverity,
	                designId,
	                netName,
	            });
	        }
	        if (opts.drawingOnlyNetSeverity && net.connections.length > 0) {
	            const reachesRealPart = net.connections.some((connection) => {
	                const module = moduleMap.get(connection.moduleRef);
	                return module !== undefined && projectModel.components[module.componentRef]?.isVirtual !== true;
	            });
	            if (!reachesRealPart) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.DrawingOnlyNet,
	                    message: `Net "${netName}" in schematic "${designId}" connects only to drawing-only placements ` +
	                        `(${net.connections.map((c) => c.moduleName).join(', ')}), so it names no resolvable module`,
	                    severity: opts.drawingOnlyNetSeverity,
	                    designId,
	                    netName,
	                });
	            }
	        }
	        if (opts.singlePinNetSeverity && net.connections.length === 1) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.SinglePinNet,
	                message: `Net "${netName}" in schematic "${designId}" has only one connection`,
	                severity: opts.singlePinNetSeverity,
	                designId,
	                netName,
	                connection: net.connections[0],
	            });
	        }
	        const connectionSet = new Set();
	        for (const connection of net.connections) {
	            const module = moduleMap.get(connection.moduleRef);
	            if (!module) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InvalidNetModuleReference,
	                    message: `Net "${netName}" in schematic "${designId}" references non-existent module "${connection.moduleRef}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    designId,
	                    netName,
	                    moduleRef: connection.moduleRef,
	                });
	                continue;
	            }
	            if (connection.moduleName !== (module.fullModuleName ?? module.name)) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InvalidConnectionModuleName,
	                    message: `Net "${netName}" in schematic "${designId}" has connection with moduleName "${connection.moduleName}" but module "${connection.moduleRef}" has name "${module.name}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    designId,
	                    netName,
	                    moduleRef: connection.moduleRef,
	                    expectedModuleName: module.name,
	                    actualModuleName: connection.moduleName,
	                });
	            }
	            const componentPins = componentPinMap.get(module.componentRef);
	            if (componentPins && !componentPins.has(connection.pinRef)) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InvalidNetPinReference,
	                    message: `Net "${netName}" in schematic "${designId}" references non-existent pin "${connection.pinRef}" on module "${connection.moduleRef}" (component "${module.componentRef}")`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    designId,
	                    netName,
	                    moduleRef: connection.moduleRef,
	                    pinRef: connection.pinRef,
	                    componentRef: module.componentRef,
	                });
	            }
	            const pinNetValue = module.pinNets[connection.pinRef];
	            if (pinNetValue?.valueOf() !== netName?.valueOf()) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InconsistentNetConnection,
	                    message: `Net "${netName}" in schematic "${designId}" is connected to module "${connection.moduleRef}" pin "${connection.pinRef}" but pinNets references net "${pinNetValue}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    designId,
	                    netName,
	                    moduleRef: connection.moduleRef,
	                    pinRef: connection.pinRef,
	                    expectedNetName: pinNetValue,
	                });
	            }
	            const connectionKey = `${connection.moduleRef}:${connection.pinRef}`;
	            if (connectionSet.has(connectionKey)) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.DuplicateNetConnection,
	                    message: `Net "${netName}" in schematic "${designId}" has duplicate connection to module "${connection.moduleRef}" pin "${connection.pinRef}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    designId,
	                    netName,
	                    moduleRef: connection.moduleRef,
	                    pinRef: connection.pinRef,
	                });
	            }
	            connectionSet.add(connectionKey);
	        }
	    }
	    const allNetConnections = new Set();
	    for (const net of Object.values(schematic.nets)) {
	        for (const connection of net.connections) {
	            allNetConnections.add(`${connection.moduleRef}:${connection.pinRef}`);
	        }
	    }
	    for (const module of schematic.modules) {
	        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
	            const connectionKey = `${module.ref}:${pinRef}`;
	            if (!allNetConnections.has(connectionKey)) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.OrphanedPinConnection,
	                    message: `Module "${module.ref}" in schematic "${designId}" has pin "${pinRef}" in pinNets but it doesn't appear in any net connections`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    designId,
	                    moduleRef: module.ref,
	                    pinRef,
	                    netName,
	                });
	            }
	            if (!schematic.nets[netName] && netName !== undefined) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.InvalidPinNetReference,
	                    message: `Module "${module.ref}" in schematic "${designId}" has pin "${pinRef}" referencing non-existent net "${netName}"`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    designId,
	                    moduleRef: module.ref,
	                    pinRef,
	                    netName,
	                });
	            }
	        }
	    }
	    const result = {
	        type: 'net-validation',
	        message: innerResults.length === 0 ?
	            `All nets in schematic "${designId}" are valid`
	            : `Net validation found issues in schematic "${designId}"`,
	        innerResults,
	        designId,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function validateMultiInstanceConfiguration(schematic, designId) {
	    const innerResults = [];
	    if (!schematic.multiInstanceConfiguration) {
	        return {
	            type: 'multi-instance-validation',
	            message: 'No multi-instance configuration to validate',
	            innerResults: [],
	        };
	    }
	    const config = schematic.multiInstanceConfiguration;
	    const instanceNames = new Set();
	    const duplicateNames = new Set();
	    for (const instance of config.instances) {
	        if (instanceNames.has(instance.instanceName)) {
	            duplicateNames.add(instance.instanceName);
	        }
	        instanceNames.add(instance.instanceName);
	    }
	    if (duplicateNames.size > 0) {
	        innerResults.push({
	            type: ProjectModelValidationErrorType.DuplicateInstanceName,
	            message: `Duplicate instance names in multi-instance schematic "${designId}": ${Array.from(duplicateNames).join(', ')}`,
	            severity: sigma_engine_shared_1.BoundaryLevel.Error,
	            designId,
	            duplicateNames: Array.from(duplicateNames),
	        });
	    }
	    for (const instance of config.instances) {
	        if (instance.instanceSheetIndex < 0 || !Number.isInteger(instance.instanceSheetIndex)) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.InvalidInstanceSheetIndex,
	                message: `Instance "${instance.instanceName}" in schematic "${designId}" has invalid sheet index: ${instance.instanceSheetIndex}`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                designId,
	                instanceName: instance.instanceName,
	                instanceSheetIndex: instance.instanceSheetIndex,
	            });
	        }
	    }
	    const result = {
	        type: 'multi-instance-validation',
	        message: innerResults.length === 0 ?
	            `Multi-instance configuration in schematic "${designId}" is valid`
	            : `Multi-instance validation found issues in schematic "${designId}"`,
	        innerResults,
	        designId,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function getNumericPinRange(designator) {
	    const range = /^(\d+)-(\d+)$/.exec(designator);
	    if (!range) {
	        return undefined;
	    }
	    const firstPin = Number(range[1]);
	    const lastPin = Number(range[2]);
	    return lastPin > firstPin ? [firstPin, lastPin] : undefined;
	}
	function countPhysicalPinsForDesignator(designator) {
	    const range = getNumericPinRange(designator);
	    return range ? range[1] - range[0] + 1 : 1;
	}
	function sourceSymbolDeclaresGroupedSignalSharing(component, pinRef, signalNames) {
	    if (!getNumericPinRange(pinRef) || new Set(signalNames).size !== signalNames.length) {
	        return false;
	    }
	    return component.symbols.some((symbol) => {
	        const declaredPinNames = new Set(symbol.pins.filter((pin) => pin.ref === pinRef).map((pin) => pin.name));
	        return signalNames.every((signalName) => declaredPinNames.has(signalName));
	    });
	}
	function validateComponents(projectModel, opts) {
	    const innerResults = [];
	    const padReferenceSeverity = projectModel.type === 'kicad' ? sigma_engine_shared_1.BoundaryLevel.Warning : sigma_engine_shared_1.BoundaryLevel.Error;
	    for (const [componentId, component] of Object.entries(projectModel.components)) {
	        if (opts.missingFootprintsSeverity && component.footprints.length === 0 && !component.isVirtual) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.MissingFootprints,
	                message: `Component "${componentId}" has no footprints`,
	                severity: opts.missingFootprintsSeverity,
	                componentId,
	            });
	        }
	        if (opts.missingSymbolsSeverity && component.symbols.length === 0) {
	            innerResults.push({
	                type: ProjectModelValidationErrorType.MissingSymbols,
	                message: `Component "${componentId}" has no symbols`,
	                severity: opts.missingSymbolsSeverity,
	                componentId,
	            });
	        }
	        if (typeof component.numPins === 'number' && component.numPins > 0) {
	            const physicalPinDesignators = new Set(component.symbols.flatMap((symbol) => symbol.pins).map((pin) => pin.ref));
	            const nonMechanicalPinDesignators = [...physicalPinDesignators].filter((designator) => !/^MT\d+$/.test(designator));
	            const actualPinCount = [...physicalPinDesignators].reduce((count, designator) => count + countPhysicalPinsForDesignator(designator), 0);
	            const actualPinCountExcludingMechanical = nonMechanicalPinDesignators.reduce((count, designator) => count + countPhysicalPinsForDesignator(designator), 0);
	            if (component.numPins !== actualPinCount && component.numPins !== actualPinCountExcludingMechanical) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.PinCountMismatch,
	                    message: `Component "${componentId}" declares ${component.numPins} pins but its unique symbol designators represent ${actualPinCount} physical pins (${actualPinCountExcludingMechanical} excluding MT mounting pins)`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    componentId,
	                    declaredCount: component.numPins,
	                    actualCount: actualPinCount,
	                    actualCountExcludingMechanical: actualPinCountExcludingMechanical,
	                });
	            }
	        }
	        if (typeof component.numPads === 'number') {
	            const actualPadCount = component.footprints.reduce((sum, footprint) => sum + footprint.pads.length, 0);
	            if (actualPadCount !== component.numPads) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.PadCountMismatch,
	                    message: `Component "${componentId}" declares ${component.numPads} pads but has ${actualPadCount} pads in footprints`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    componentId,
	                    declaredCount: component.numPads,
	                    actualCount: actualPadCount,
	                });
	            }
	        }
	        const allPinRefs = new Set();
	        for (const symbol of component.symbols) {
	            for (const pin of symbol.pins) {
	                allPinRefs.add(pin.ref);
	            }
	        }
	        const allPadRefs = new Set();
	        for (const footprint of component.footprints) {
	            for (const pad of footprint.pads) {
	                allPadRefs.add(pad.ref);
	            }
	        }
	        for (const signal of component.signals) {
	            for (const pinRef of signal.pinRefs) {
	                if (pinRef !== null && !allPinRefs.has(pinRef)) {
	                    innerResults.push({
	                        type: ProjectModelValidationErrorType.InvalidSignalPinReference,
	                        message: `Signal "${signal.name}" in component "${componentId}" references non-existent pin "${pinRef}"`,
	                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                        componentId,
	                        signalName: signal.name,
	                        pinRef,
	                    });
	                }
	            }
	            for (const padRef of signal.padRefs) {
	                if (!allPadRefs.has(padRef)) {
	                    innerResults.push({
	                        type: ProjectModelValidationErrorType.InvalidSignalPadReference,
	                        message: `Signal "${signal.name}" in component "${componentId}" references non-existent pad "${padRef}"`,
	                        severity: padReferenceSeverity,
	                        componentId,
	                        signalName: signal.name,
	                        padRef,
	                    });
	                }
	            }
	        }
	        const pinToSignals = new Map();
	        for (const signal of component.signals) {
	            for (const pinRef of signal.pinRefs) {
	                if (pinRef !== null) {
	                    const signals = pinToSignals.get(pinRef) ?? [];
	                    signals.push(signal.name);
	                    pinToSignals.set(pinRef, signals);
	                }
	            }
	        }
	        for (const [pinRef, signalNames] of pinToSignals) {
	            if (signalNames.length > 1 && !sourceSymbolDeclaresGroupedSignalSharing(component, pinRef, signalNames)) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.DuplicatePinInSignals,
	                    message: `Pin "${pinRef}" in component "${componentId}" is referenced by multiple signals: ${signalNames.map((s) => `"${s}"`).join(', ')}`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    componentId,
	                    pinRef,
	                    signalNames,
	                });
	            }
	        }
	    }
	    const result = {
	        type: 'component-validation',
	        message: innerResults.length === 0 ? 'All components are valid' : 'Component validation found issues',
	        innerResults,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	function validateStructuralIssues(projectModel, opts) {
	    const innerResults = [];
	    const usedComponents = new Set();
	    const usedFootprints = new Map();
	    for (const module of projectModel.modules) {
	        usedComponents.add(module.componentRef);
	    }
	    for (const design of Object.values(projectModel.designs)) {
	        for (const module of design.modules) {
	            usedComponents.add(module.componentRef);
	            if (design.modelType === 'pcb') {
	                if (!usedFootprints.has(module.componentRef)) {
	                    usedFootprints.set(module.componentRef, new Set());
	                }
	                usedFootprints.get(module.componentRef).add(module.footprintRef);
	            }
	        }
	    }
	    if (opts.unusedComponentSeverity) {
	        for (const componentId of Object.keys(projectModel.components)) {
	            if (!usedComponents.has(componentId)) {
	                innerResults.push({
	                    type: ProjectModelValidationErrorType.UnusedComponent,
	                    message: `Component "${componentId}" is not used by any module`,
	                    severity: opts.unusedComponentSeverity,
	                    componentId,
	                });
	            }
	        }
	    }
	    if (opts.unusedFootprintSeverity) {
	        for (const [componentId, component] of Object.entries(projectModel.components)) {
	            const usedFootprintRefs = usedFootprints.get(componentId) ?? new Set();
	            for (const footprint of component.footprints) {
	                if (!usedFootprintRefs.has(footprint.ref)) {
	                    innerResults.push({
	                        type: ProjectModelValidationErrorType.UnusedFootprint,
	                        message: `Footprint "${footprint.ref}" in component "${componentId}" is not used by any module`,
	                        severity: opts.unusedFootprintSeverity,
	                        componentId,
	                        footprintRef: footprint.ref,
	                    });
	                }
	            }
	        }
	    }
	    const result = {
	        type: 'structural-validation',
	        message: innerResults.length === 0 ? 'No structural issues found' : 'Structural validation found issues',
	        innerResults,
	    };
	    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
	    return result;
	}
	
	return validateProjectModel;
}

var hasRequiredModelValidation;

function requireModelValidation () {
	if (hasRequiredModelValidation) return ModelValidation;
	hasRequiredModelValidation = 1;
	(function (exports) {
		var __createBinding = (ModelValidation && ModelValidation.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ModelValidation && ModelValidation.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireValidateProjectModel(), exports);
		
	} (ModelValidation));
	return ModelValidation;
}

var validation = {};

var kicadValidation = {};

var boardValidate = {};

var validateKicadV6SchemaFile = {};

var hasRequiredValidateKicadV6SchemaFile;

function requireValidateKicadV6SchemaFile () {
	if (hasRequiredValidateKicadV6SchemaFile) return validateKicadV6SchemaFile;
	hasRequiredValidateKicadV6SchemaFile = 1;
	Object.defineProperty(validateKicadV6SchemaFile, "__esModule", { value: true });
	validateKicadV6SchemaFile.validateKicadV6SchemaFile = validateKicadV6SchemaFile$1;
	const NodeParser_1 = requireNodeParser();
	const Util_1 = requireUtil();
	const constants_1 = requireConstants();
	const v6ExtractSheetInfo_1 = requireV6ExtractSheetInfo();
	const v6ParseTitleBlock_1 = requireV6ParseTitleBlock();
	function validateKicadV6SchemaFile$1(fileContents, parseSheets, allowMissingVersion) {
	    let version;
	    let referencedFiles = [];
	    const metadata = {};
	    const tree = NodeParser_1.SExpressionFileParser.Parse(fileContents);
	    tree.nodes.forEach((node) => {
	        switch (node.name) {
	            case 'version': {
	                version = (0, Util_1.removeQuotes)(node.values[0]);
	                break;
	            }
	            case 'title_block': {
	                const titleBlock = (0, v6ParseTitleBlock_1.extractTitleBlock)(node);
	                for (const [key, value] of Object.entries(titleBlock)) {
	                    if (typeof value !== 'string') {
	                        continue;
	                    }
	                    metadata[`kicad${key}`] = value;
	                }
	                break;
	            }
	            case 'sheet': {
	                if (!parseSheets) {
	                    break;
	                }
	                const sheet = (0, v6ExtractSheetInfo_1.extractSheetInfo)(node);
	                if (sheet.sheetfile) {
	                    if (!referencedFiles) {
	                        referencedFiles = [];
	                    }
	                    if (referencedFiles.indexOf(sheet.sheetfile) === -1) {
	                        referencedFiles.push(sheet.sheetfile);
	                    }
	                }
	                break;
	            }
	            case 'sheet_instances': {
	                if (!parseSheets) {
	                    break;
	                }
	                const pathNode = node.nodes.find((n) => n.name === 'path');
	                if (pathNode?.values[0] === '"/"') {
	                    const pageNode = pathNode.nodes.find((n) => n.name === 'page');
	                    {
	                        if (pageNode?.values[0] === '"1"') {
	                            metadata[constants_1.MetadataKicadIsRoot] = String(true);
	                        }
	                    }
	                }
	                break;
	            }
	        }
	    });
	    if (!version && !allowMissingVersion) {
	        throw new Error('Kicad file missing version information');
	    }
	    return {
	        version,
	        referencedFiles,
	        metadata,
	    };
	}
	
	return validateKicadV6SchemaFile;
}

var hasRequiredBoardValidate;

function requireBoardValidate () {
	if (hasRequiredBoardValidate) return boardValidate;
	hasRequiredBoardValidate = 1;
	Object.defineProperty(boardValidate, "__esModule", { value: true });
	boardValidate.validateKicadPcb = validateKicadPcb;
	const validateKicadV6SchemaFile_1 = requireValidateKicadV6SchemaFile();
	const constants_1 = requireConstants();
	function validateKicadPcb(filename, fileContents) {
	    if (!filename.endsWith('.kicad_pcb') || !fileContents.startsWith(constants_1.KicadPcbPreamble)) {
	        throw new Error('Invalid kicad pcb');
	    }
	    return (0, validateKicadV6SchemaFile_1.validateKicadV6SchemaFile)(fileContents, false);
	}
	
	return boardValidate;
}

var projectValidation = {};

var hasRequiredProjectValidation;

function requireProjectValidation () {
	if (hasRequiredProjectValidation) return projectValidation;
	hasRequiredProjectValidation = 1;
	Object.defineProperty(projectValidation, "__esModule", { value: true });
	projectValidation.validateKicadProjectFile = validateKicadProjectFile;
	const Utils_1 = requireUtils$4();
	const kicad_file_type_1 = requireKicadFileType();
	function validateKicadProjectFile(filename, fileContents) {
	    const parsed = JSON.parse(fileContents);
	    if (filename.endsWith(kicad_file_type_1.KicadFileType.KicadProject)) {
	        const projectSettings = parsed;
	        const referencedFiles = [
	            ...(0, Utils_1.toArray)(projectSettings.pcbnew?.page_layout_descr_file),
	            ...(0, Utils_1.toArray)(projectSettings.schematic?.ngspice?.workbook_filename),
	            ...(0, Utils_1.toArray)(projectSettings.schematic?.page_layout_descr_file),
	        ].filter(Boolean);
	        return {
	            version: projectSettings.meta?.version?.toString(),
	            referencedFiles,
	            metadata: {},
	        };
	    }
	    if (filename.endsWith(kicad_file_type_1.KicadFileType.KicadProjectLocalSettings)) {
	        const projectSettings = parsed;
	        const referencedFiles = [];
	        return {
	            version: projectSettings.meta?.version?.toString(),
	            referencedFiles,
	            metadata: {},
	        };
	    }
	    throw new Error(`Invalid project file: ${filename}`);
	}
	
	return projectValidation;
}

var v6schValidate = {};

var hasRequiredV6schValidate;

function requireV6schValidate () {
	if (hasRequiredV6schValidate) return v6schValidate;
	hasRequiredV6schValidate = 1;
	Object.defineProperty(v6schValidate, "__esModule", { value: true });
	v6schValidate.validateKicadSch = validateKicadSch;
	const constants_1 = requireConstants();
	const validateKicadV6SchemaFile_1 = requireValidateKicadV6SchemaFile();
	function validateKicadSch(filename, fileContents) {
	    if (!filename.endsWith('.kicad_sch') || !fileContents.startsWith(constants_1.KicadSchPreamble)) {
	        throw new Error('Invalid kicad sch');
	    }
	    const result = (0, validateKicadV6SchemaFile_1.validateKicadV6SchemaFile)(fileContents, true);
	    if (filename.startsWith('_autosave-')) {
	        result.skipReason = 'Autosave files are not supported';
	    }
	    return result;
	}
	
	return v6schValidate;
}

var hasRequiredKicadValidation;

function requireKicadValidation () {
	if (hasRequiredKicadValidation) return kicadValidation;
	hasRequiredKicadValidation = 1;
	Object.defineProperty(kicadValidation, "__esModule", { value: true });
	kicadValidation.getKicadFileType = getKicadFileType;
	kicadValidation.validateKicadFile = validateKicadFile;
	kicadValidation.detectKicadProjects = detectKicadProjects;
	const project_detection_1 = requireProjectDetection();
	const boardValidate_1 = requireBoardValidate();
	const validateKicadV6SchemaFile_1 = requireValidateKicadV6SchemaFile();
	const constants_1 = requireConstants();
	const projectValidation_1 = requireProjectValidation();
	const v6schValidate_1 = requireV6schValidate();
	const kicad_file_type_1 = requireKicadFileType();
	const kicadNetlistValidation_1 = requireKicadNetlistValidation();
	function getKicadFileType(filename, fileContents) {
	    const path = filename.split('/');
	    if (path.length < 1) {
	        return false;
	    }
	    const parts = path[path.length - 1].split('.');
	    if (parts.length < 1) {
	        return false;
	    }
	    let ext = parts[parts.length - 1].toLowerCase();
	    if (parts.length > 1) {
	        ext = '.' + ext;
	    }
	    switch (ext) {
	        case '.kicad_pcb':
	            if (fileContents.startsWith(constants_1.KicadPcbPreamble)) {
	                return kicad_file_type_1.KicadFileType.KicadPcb;
	            }
	            break;
	        case '.kicad_sch':
	            if (fileContents.startsWith(constants_1.KicadSchPreamble)) {
	                return kicad_file_type_1.KicadFileType.KicadSch;
	            }
	            break;
	        case '.sch':
	            if (fileContents.startsWith(constants_1.EESchemaSchPreamble)) {
	                return kicad_file_type_1.KicadFileType.EESchemaSch;
	            }
	            break;
	        case '.lib':
	            if (fileContents.startsWith(constants_1.EESchemaLibPreamble)) {
	                return kicad_file_type_1.KicadFileType.EESchemaLib;
	            }
	            break;
	        case 'fp-lib-table':
	            if (fileContents.startsWith(constants_1.KicadFootprintLibTablePreamble)) {
	                return kicad_file_type_1.KicadFileType.KicadFootprintLibTable;
	            }
	            break;
	        case 'sym-lib-table':
	            if (fileContents.startsWith(constants_1.KicadSymbolLibTablePreamble)) {
	                return kicad_file_type_1.KicadFileType.KicadSymbolLibTable;
	            }
	            break;
	        case '.dcm':
	            if (fileContents.startsWith(constants_1.KicadDocLibPreamble)) {
	                return kicad_file_type_1.KicadFileType.KicadDocLib;
	            }
	            break;
	        case '.kicad_pro':
	            try {
	                const contents = JSON.parse(fileContents);
	                if (contents.meta?.filename === filename) {
	                    return kicad_file_type_1.KicadFileType.KicadProject;
	                }
	            }
	            catch {
	            }
	            break;
	        case '.kicad_prl':
	            try {
	                const contents = JSON.parse(fileContents);
	                if (contents.meta?.filename === filename) {
	                    return kicad_file_type_1.KicadFileType.KicadProjectLocalSettings;
	                }
	            }
	            catch {
	            }
	            break;
	        case '.net':
	            return kicad_file_type_1.KicadFileType.KicadNetlist;
	    }
	    return false;
	}
	function validateKicadFile(filename, fileContents) {
	    const type = getKicadFileType(filename, fileContents);
	    if (!type) {
	        return {
	            isValid: false,
	            data: undefined,
	            errors: [`Invalid KiCad file or contents ${filename}`],
	        };
	    }
	    let validationResult;
	    let semanticType;
	    switch (type) {
	        case kicad_file_type_1.KicadFileType.KicadSch:
	            validationResult = (0, v6schValidate_1.validateKicadSch)(filename, fileContents);
	            semanticType = 'schematic';
	            break;
	        case kicad_file_type_1.KicadFileType.KicadPcb:
	            validationResult = (0, boardValidate_1.validateKicadPcb)(filename, fileContents);
	            semanticType = 'board';
	            break;
	        case kicad_file_type_1.KicadFileType.EESchemaSch:
	            return {
	                isValid: false,
	                data: undefined,
	                semanticType: 'schematic',
	                errors: ['KiCad v5 schematics are not supported. Please upgrade to KiCad v6+'],
	            };
	        case kicad_file_type_1.KicadFileType.KicadFootprintLibTable:
	        case kicad_file_type_1.KicadFileType.KicadSymbolLibTable:
	            validationResult = (0, validateKicadV6SchemaFile_1.validateKicadV6SchemaFile)(fileContents, false, true);
	            semanticType = 'other';
	            break;
	        case kicad_file_type_1.KicadFileType.KicadDocLib:
	        case kicad_file_type_1.KicadFileType.EESchemaLib:
	            return {
	                isValid: false,
	                data: undefined,
	                semanticType: 'lib',
	                errors: ['KiCad v5 libraries are not supported. Please upgrade to KiCad v6+'],
	            };
	        case kicad_file_type_1.KicadFileType.KicadProject:
	        case kicad_file_type_1.KicadFileType.KicadProjectLocalSettings:
	            validationResult = (0, projectValidation_1.validateKicadProjectFile)(filename, fileContents);
	            semanticType = 'project';
	            break;
	        case kicad_file_type_1.KicadFileType.KicadNetlist:
	            validationResult = (0, kicadNetlistValidation_1.validateKicadNetlist)(filename, fileContents);
	            semanticType = 'netlist';
	            break;
	        default:
	            semanticType = 'other';
	            validationResult = {
	                metadata: {},
	                referencedFiles: [],
	            };
	    }
	    if (validationResult.skipReason) {
	        return {
	            isValid: false,
	            semanticType,
	            errors: [validationResult.skipReason],
	        };
	    }
	    return {
	        isValid: true,
	        data: {
	            semanticType: semanticType,
	            fileFormat: {
	                cadTool: 'kicad',
	                type: type,
	                version: validationResult.version,
	            },
	            referencedFiles: validationResult.referencedFiles,
	            metadata: validationResult.metadata,
	        },
	    };
	}
	function detectKicadProjects(files, fileDetails) {
	    return (0, project_detection_1.detectAnchoredProjects)({
	        tool: 'kicad',
	        toolDisplayName: 'KiCad',
	        files,
	        fileDetails,
	        primaryAnchorMatch: (0, project_detection_1.endsWithExt)('.kicad_pro'),
	        primaryAnchorLabel: '.kicad_pro',
	        secondaryAnchorMatch: (0, project_detection_1.endsWithExt)('.kicad_prl'),
	        secondaryAnchorLabel: '.kicad_prl',
	    });
	}
	
	return kicadValidation;
}

var eagleValidation = {};

var hasRequiredEagleValidation;

function requireEagleValidation () {
	if (hasRequiredEagleValidation) return eagleValidation;
	hasRequiredEagleValidation = 1;
	Object.defineProperty(eagleValidation, "__esModule", { value: true });
	eagleValidation.EagleFileType = void 0;
	eagleValidation.getEagleFileType = getEagleFileType;
	eagleValidation.validateEagleFile = validateEagleFile;
	eagleValidation.detectEagleProjects = detectEagleProjects;
	const xml_js_1 = requireLib$1z();
	const project_detection_1 = requireProjectDetection();
	var EagleFileType;
	(function (EagleFileType) {
	    EagleFileType["EagleBrd"] = ".brd";
	    EagleFileType["EagleSch"] = ".sch";
	    EagleFileType["EagleLbr"] = ".lbr";
	    EagleFileType["EagleDru"] = ".dru";
	})(EagleFileType || (eagleValidation.EagleFileType = EagleFileType = {}));
	function getEagleFileType(filename, fileContents) {
	    const path = filename.split('/');
	    if (path.length < 1) {
	        return false;
	    }
	    const parts = path[path.length - 1].split('.');
	    if (parts.length < 1) {
	        return false;
	    }
	    const ext = '.' + parts[parts.length - 1].toLowerCase();
	    switch (ext) {
	        case '.brd':
	        case '.sch':
	        case '.lbr':
	            if (fileContents.trim().startsWith('<?xml') && fileContents.indexOf('eagle') !== -1) {
	                return ext;
	            }
	            break;
	        case '.dru':
	            return EagleFileType.EagleDru;
	    }
	    return false;
	}
	function eagleSemanticType(type) {
	    switch (type) {
	        case EagleFileType.EagleSch:
	            return 'schematic';
	        case EagleFileType.EagleBrd:
	            return 'board';
	        case EagleFileType.EagleLbr:
	            return 'lib';
	        case EagleFileType.EagleDru:
	            return 'dru';
	        default:
	            return 'other';
	    }
	}
	function validateEagleFile(filename, fileContents) {
	    const type = getEagleFileType(filename, fileContents);
	    if (!type) {
	        return {
	            isValid: false,
	            errors: [`Invalid eagle file or contents ${filename}`],
	        };
	    }
	    const semanticType = eagleSemanticType(type);
	    try {
	        const validationResult = validateEagleFileContents(fileContents, type);
	        if (!validationResult) {
	            return {
	                isValid: false,
	                semanticType,
	                errors: [`Invalid eagle file or contents ${filename}`],
	            };
	        }
	        return {
	            isValid: true,
	            data: {
	                semanticType: validationResult.type || 'other',
	                fileFormat: {
	                    cadTool: 'eagle',
	                    type: type,
	                    version: validationResult?.version,
	                },
	                referencedFiles: [],
	                encoding: validationResult?.encoding,
	                metadata: {},
	            },
	        };
	    }
	    catch (e) {
	        if (e instanceof Error || (e && typeof e === 'object' && typeof e.message === 'string')) {
	            return {
	                isValid: false,
	                semanticType,
	                errors: [e.message],
	            };
	        }
	    }
	    return {
	        isValid: false,
	        semanticType,
	        errors: ['Could not parse file'],
	    };
	}
	function detectEagleProjects(files, fileDetails) {
	    const eagleFiles = fileDetails.filter((d) => d.cadTool === 'eagle');
	    const warnings = [];
	    const blobMap = (0, project_detection_1.buildBlobMap)(files);
	    if (eagleFiles.length === 0) {
	        return { projects: [], warnings };
	    }
	    const groupsByKey = new Map();
	    for (const detail of eagleFiles) {
	        const lower = detail.relativePath.toLowerCase();
	        if (!lower.endsWith('.sch') && !lower.endsWith('.brd')) {
	            continue;
	        }
	        const dir = (0, project_detection_1.dirnameForward)(detail.relativePath);
	        const base = (0, project_detection_1.basenameWithoutExtension)(detail.relativePath);
	        const key = (0, project_detection_1.normalizePathForCompare)(`${dir}/${base}`);
	        if (!groupsByKey.has(key)) {
	            groupsByKey.set(key, {
	                name: dir === '' || dir === '.' ? base : `${dir}/${base}`,
	                homeDir: dir,
	                baseKey: base,
	                files: [],
	            });
	        }
	    }
	    if (groupsByKey.size === 0) {
	        warnings.push('No Eagle schematic or board files found to anchor a project definition.');
	        return {
	            projects: [(0, project_detection_1.buildProjectDefinition)('eagle', undefined, eagleFiles, blobMap)],
	            warnings,
	        };
	    }
	    const groups = Array.from(groupsByKey.values());
	    for (const detail of eagleFiles) {
	        const lower = detail.relativePath.toLowerCase();
	        const dir = (0, project_detection_1.dirnameForward)(detail.relativePath);
	        const base = (0, project_detection_1.basenameWithoutExtension)(detail.relativePath);
	        if (lower.endsWith('.sch') || lower.endsWith('.brd')) {
	            const target = groups.find((g) => (0, project_detection_1.normalizePathForCompare)(g.homeDir) === (0, project_detection_1.normalizePathForCompare)(dir) &&
	                g.baseKey.toLowerCase() === base.toLowerCase());
	            if (target) {
	                target.files.push(detail);
	            }
	            continue;
	        }
	        const sameDir = groups.filter((g) => (0, project_detection_1.normalizePathForCompare)(g.homeDir) === (0, project_detection_1.normalizePathForCompare)(dir));
	        if (sameDir.length > 0) {
	            for (const g of sameDir) {
	                g.files.push(detail);
	            }
	            continue;
	        }
	        let best;
	        let bestLen = -1;
	        for (const g of groups) {
	            if ((0, project_detection_1.isSameOrDescendantDir)(dir, g.homeDir) && g.homeDir.length > bestLen) {
	                best = g;
	                bestLen = g.homeDir.length;
	            }
	        }
	        (best ?? groups[0]).files.push(detail);
	    }
	    const projects = groups.map((g) => (0, project_detection_1.buildProjectDefinition)('eagle', g.name, g.files, blobMap));
	    return { projects, warnings };
	}
	function validateEagleFileContents(fileContents, type) {
	    if (type === EagleFileType.EagleDru) {
	        return {
	            type: 'dru',
	            version: '*',
	            encoding: 'utf8',
	        };
	    }
	    const xml = (0, xml_js_1.xml2js)(fileContents, { compact: true, addParent: false });
	    let result;
	    if (typeof xml.eagle === 'object') {
	        let brdRoot = xml;
	        result = {
	            encoding: brdRoot._declaration._attributes.encoding,
	            version: brdRoot.eagle._attributes.version,
	        };
	    }
	    if (!result) {
	        return false;
	    }
	    switch (type) {
	        case EagleFileType.EagleSch: {
	            const schXml = xml;
	            if (!schXml.eagle.drawing.schematic) {
	                return false;
	            }
	            result.type = 'schematic';
	            break;
	        }
	        case EagleFileType.EagleBrd: {
	            const brdXml = xml;
	            if (!brdXml.eagle.drawing.board) {
	                return false;
	            }
	            result.type = 'board';
	            break;
	        }
	        case EagleFileType.EagleLbr: {
	            const libXml = xml;
	            if (!libXml.eagle.drawing.library) {
	                return false;
	            }
	            result.type = 'lib';
	            break;
	        }
	    }
	    return result;
	}
	
	return eagleValidation;
}

var xpeditionValidation = {};

var hasRequiredXpeditionValidation;

function requireXpeditionValidation () {
	if (hasRequiredXpeditionValidation) return xpeditionValidation;
	hasRequiredXpeditionValidation = 1;
	Object.defineProperty(xpeditionValidation, "__esModule", { value: true });
	xpeditionValidation.XpeditionFileType = void 0;
	xpeditionValidation.getXpeditionFileType = getXpeditionFileType;
	xpeditionValidation.validateXpeditionFile = validateXpeditionFile;
	xpeditionValidation.detectXpeditionProjects = detectXpeditionProjects;
	const project_detection_1 = requireProjectDetection();
	var XpeditionFileType;
	(function (XpeditionFileType) {
	    XpeditionFileType["Project"] = "xpedition-project";
	    XpeditionFileType["Telesis"] = "xpedition-telesis-netlist";
	    XpeditionFileType["FlatNet"] = "xpedition-flat-net";
	})(XpeditionFileType || (xpeditionValidation.XpeditionFileType = XpeditionFileType = {}));
	const EXTENSION_FILE_TYPES = new Map([
	    ['.prj', XpeditionFileType.Project],
	    ['.tel', XpeditionFileType.Telesis],
	    ['.qcv', XpeditionFileType.FlatNet],
	]);
	function getXpeditionFileType(filename) {
	    const lower = filename.toLowerCase();
	    const dotIndex = lower.lastIndexOf('.');
	    if (dotIndex === -1) {
	        return false;
	    }
	    return EXTENSION_FILE_TYPES.get(lower.slice(dotIndex)) ?? false;
	}
	function semanticTypeFor(type) {
	    switch (type) {
	        case XpeditionFileType.Project:
	            return 'project';
	        case XpeditionFileType.Telesis:
	            return 'schematic';
	        case XpeditionFileType.FlatNet:
	            return 'netlist';
	    }
	}
	function validateXpeditionFile(filename) {
	    const type = getXpeditionFileType(filename);
	    if (type === false) {
	        return { isValid: false, errors: [`Not a recognized Xpedition file: ${filename}`] };
	    }
	    return {
	        isValid: true,
	        data: {
	            semanticType: semanticTypeFor(type),
	            fileFormat: { cadTool: 'xpedition', type },
	            referencedFiles: [],
	            metadata: {},
	        },
	    };
	}
	function detectXpeditionProjects(files, fileDetails) {
	    return (0, project_detection_1.detectAnchoredProjects)({
	        tool: 'xpedition',
	        toolDisplayName: 'Xpedition',
	        files,
	        fileDetails,
	        primaryAnchorMatch: (0, project_detection_1.endsWithExt)('.prj'),
	        primaryAnchorLabel: '.prj',
	    });
	}
	
	return xpeditionValidation;
}

var hasRequiredValidation;

function requireValidation () {
	if (hasRequiredValidation) return validation;
	hasRequiredValidation = 1;
	Object.defineProperty(validation, "__esModule", { value: true });
	validation.isLikelyCadFile = isLikelyCadFile;
	validation.validateFile = validateFile;
	validation.getSupportedFileTypes = getSupportedFileTypes;
	validation.validateFileType = validateFileType;
	validation.validateFileExtensionSupported = validateFileExtensionSupported;
	validation.detectProjectsForTool = detectProjectsForTool;
	const altiumValidation_1 = requireAltiumValidation();
	const altiumFileTypes_1 = requireAltiumFileTypes();
	const kicadValidation_1 = requireKicadValidation();
	const kicad_file_type_1 = requireKicadFileType();
	const eagleValidation_1 = requireEagleValidation();
	const orcadValidation_1 = requireOrcadValidation();
	const allegroValidation_1 = requireAllegroValidation();
	const xpeditionValidation_1 = requireXpeditionValidation();
	const TEXT_CAD_EXTENSIONS = new Set([
	    '.kicad_pcb',
	    '.kicad_sch',
	    '.sch',
	    '.lib',
	    '.brd',
	    '.dcm',
	    '.kicad_pro',
	    '.kicad_prl',
	    '.net',
	    '.lbr',
	    '.dru',
	    '.opj',
	    '.dsn',
	    '.xml',
	    '.onl',
	]);
	const TEXT_CAD_EXACT_FILENAMES = new Set(['fp-lib-table', 'sym-lib-table']);
	function couldBeTextCadFile(filename) {
	    const lower = filename.toLowerCase();
	    const basename = lower.includes('/') ? lower.slice(lower.lastIndexOf('/') + 1) : lower;
	    if (TEXT_CAD_EXACT_FILENAMES.has(basename)) {
	        return true;
	    }
	    const dotIndex = basename.lastIndexOf('.');
	    return dotIndex !== -1 && TEXT_CAD_EXTENSIONS.has(basename.slice(dotIndex));
	}
	function isOrCadDsnFile(filename) {
	    return filename.toLowerCase().endsWith(orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic);
	}
	function isLikelyCadFile(filename) {
	    return (!!(0, altiumValidation_1.getAltiumFileType)(filename) ||
	        !!(0, allegroValidation_1.getAllegroFileType)(filename) ||
	        !!(0, xpeditionValidation_1.getXpeditionFileType)(filename) ||
	        couldBeTextCadFile(filename));
	}
	function withClaimedTools(result, claimedTools) {
	    if (result.isValid || claimedTools.length === 0) {
	        return result;
	    }
	    return { ...result, claimedTools: [...new Set(claimedTools)] };
	}
	async function validateFile(filename, fileContents) {
	    if (!filename || filename.length <= 1 || !fileContents) {
	        return {
	            isValid: false,
	            data: undefined,
	            errors: [`Invalid file name or contents ${filename}`],
	        };
	    }
	    const claimedTools = [];
	    let lastValidationResult;
	    if ((0, altiumValidation_1.getAltiumFileType)(filename)) {
	        claimedTools.push('altium');
	        lastValidationResult = await (0, altiumValidation_1.validateAltiumFile)(filename, fileContents);
	        if (lastValidationResult.isValid) {
	            return lastValidationResult;
	        }
	    }
	    if ((0, xpeditionValidation_1.getXpeditionFileType)(filename)) {
	        return (0, xpeditionValidation_1.validateXpeditionFile)(filename);
	    }
	    if (isOrCadDsnFile(filename)) {
	        claimedTools.push('orcad');
	        return withClaimedTools(await (0, orcadValidation_1.validateOrCADXFile)(filename, new Uint8Array(await fileContents.arrayBuffer())), claimedTools);
	    }
	    if ((0, allegroValidation_1.getAllegroFileType)(filename) === allegroValidation_1.AllegroFileType.Cdsz) {
	        claimedTools.push('allegro');
	        return withClaimedTools((0, allegroValidation_1.validateAllegroFile)(filename, new Uint8Array(await fileContents.arrayBuffer())), claimedTools);
	    }
	    if (!couldBeTextCadFile(filename) && !(0, allegroValidation_1.getAllegroFileType)(filename)) {
	        return withClaimedTools(lastValidationResult ?? {
	            isValid: false,
	            data: undefined,
	            errors: [`Unrecognized file type ${filename}`],
	        }, claimedTools);
	    }
	    const textContents = await fileContents.text();
	    if ((0, kicadValidation_1.getKicadFileType)(filename, textContents)) {
	        claimedTools.push('kicad');
	        lastValidationResult = (0, kicadValidation_1.validateKicadFile)(filename, textContents);
	        if (lastValidationResult.isValid) {
	            return lastValidationResult;
	        }
	    }
	    if ((0, orcadValidation_1.getOrCADXFileType)(filename, textContents)) {
	        claimedTools.push('orcad');
	        lastValidationResult = await (0, orcadValidation_1.validateOrCADXFile)(filename, textContents);
	        if (lastValidationResult.isValid) {
	            return lastValidationResult;
	        }
	    }
	    if ((0, eagleValidation_1.getEagleFileType)(filename, textContents)) {
	        claimedTools.push('eagle');
	        lastValidationResult = (0, eagleValidation_1.validateEagleFile)(filename, textContents);
	        if (lastValidationResult.isValid) {
	            return lastValidationResult;
	        }
	    }
	    if ((0, allegroValidation_1.getAllegroFileType)(filename, textContents)) {
	        claimedTools.push('allegro');
	        lastValidationResult = (0, allegroValidation_1.validateAllegroFile)(filename, textContents);
	        if (lastValidationResult.isValid) {
	            return lastValidationResult;
	        }
	    }
	    return withClaimedTools(lastValidationResult ?? {
	        isValid: false,
	        data: undefined,
	        errors: [`Unrecognized file type ${filename}`],
	    }, claimedTools);
	}
	function getSupportedFileTypes() {
	    return {
	        kicad: [
	            kicad_file_type_1.KicadFileType.EESchemaBrd,
	            kicad_file_type_1.KicadFileType.KicadPcb,
	            kicad_file_type_1.KicadFileType.KicadSch,
	            kicad_file_type_1.KicadFileType.EESchemaSch,
	            kicad_file_type_1.KicadFileType.EESchemaLib,
	            kicad_file_type_1.KicadFileType.KicadFootprintLibTable,
	            kicad_file_type_1.KicadFileType.KicadSymbolLibTable,
	            kicad_file_type_1.KicadFileType.KicadDocLib,
	            kicad_file_type_1.KicadFileType.KicadProject,
	            kicad_file_type_1.KicadFileType.KicadProjectLocalSettings,
	        ],
	        eagle: [eagleValidation_1.EagleFileType.EagleBrd, eagleValidation_1.EagleFileType.EagleSch, eagleValidation_1.EagleFileType.EagleLbr, eagleValidation_1.EagleFileType.EagleDru],
	        altium: [
	            altiumFileTypes_1.AltiumFileType.Sch,
	            altiumFileTypes_1.AltiumFileType.SchDoc,
	            altiumFileTypes_1.AltiumFileType.PcbDoc,
	            altiumFileTypes_1.AltiumFileType.Project,
	            altiumFileTypes_1.AltiumFileType.ProjectStructure,
	            altiumFileTypes_1.AltiumFileType.ProjectVariants,
	            altiumFileTypes_1.AltiumFileType.OutJob,
	            altiumFileTypes_1.AltiumFileType.PcbDrawing,
	            altiumFileTypes_1.AltiumFileType.Harness,
	            altiumFileTypes_1.AltiumFileType.BomDoc,
	            altiumFileTypes_1.AltiumFileType.Stackup,
	            altiumFileTypes_1.AltiumFileType.Netlist,
	        ],
	    };
	}
	async function validateFileType(filename, fileContents) {
	    if (!filename || filename.length <= 1 || !fileContents) {
	        return false;
	    }
	    if ((0, altiumValidation_1.getAltiumFileType)(filename)) {
	        return true;
	    }
	    if (isOrCadDsnFile(filename)) {
	        return true;
	    }
	    if ((0, xpeditionValidation_1.getXpeditionFileType)(filename)) {
	        return true;
	    }
	    if ((0, allegroValidation_1.getAllegroFileType)(filename)) {
	        return true;
	    }
	    if (!couldBeTextCadFile(filename)) {
	        return false;
	    }
	    const textContents = await fileContents.text();
	    if ((0, kicadValidation_1.getKicadFileType)(filename, textContents)) {
	        return true;
	    }
	    if ((0, orcadValidation_1.getOrCADXFileType)(filename, textContents)) {
	        return true;
	    }
	    if ((0, eagleValidation_1.getEagleFileType)(filename, textContents)) {
	        return true;
	    }
	    return false;
	}
	function validateFileExtensionSupported(filename, allowedExtensions) {
	    if (!filename) {
	        return false;
	    }
	    filename = filename.toLowerCase();
	    return allowedExtensions.some((e) => e.includes('.') ? filename.endsWith(e.toLowerCase()) : filename === e.toLowerCase());
	}
	function detectProjectsForTool(tool, files, fileDetails) {
	    switch (tool) {
	        case 'kicad':
	            return (0, kicadValidation_1.detectKicadProjects)(files, fileDetails);
	        case 'altium':
	            return (0, altiumValidation_1.detectAltiumProjects)(files, fileDetails);
	        case 'eagle':
	            return (0, eagleValidation_1.detectEagleProjects)(files, fileDetails);
	        case 'orcad':
	            return (0, orcadValidation_1.detectOrcadProjects)(files, fileDetails);
	        case 'allegro':
	            return (0, allegroValidation_1.detectAllegroProjects)(files, fileDetails);
	        case 'xpedition':
	            return (0, xpeditionValidation_1.detectXpeditionProjects)(files, fileDetails);
	        default:
	            return { projects: [], warnings: [] };
	    }
	}
	
	return validation;
}

var requiredFiles = {};

var xpeditionRequiredFiles = {};

var hasRequiredXpeditionRequiredFiles;

function requireXpeditionRequiredFiles () {
	if (hasRequiredXpeditionRequiredFiles) return xpeditionRequiredFiles;
	hasRequiredXpeditionRequiredFiles = 1;
	Object.defineProperty(xpeditionRequiredFiles, "__esModule", { value: true });
	xpeditionRequiredFiles.checkXpeditionRequiredFiles = checkXpeditionRequiredFiles;
	const sigma_engine_shared_1 = requireDist$1();
	const PROJECT_EXT = '.prj';
	const NETLIST_EXTS = ['.tel', '.qcv'];
	const NETLIST_DISPLAY_EXT = '.tel or .qcv';
	function missingFinding(requiredFileExt, noun, triggeringFile) {
	    return {
	        type: 'required-file-check',
	        severity: sigma_engine_shared_1.BoundaryLevel.Error,
	        message: `An Xpedition upload needs ${noun}, which is not in the uploaded files`,
	        rule: 'xpedition-required-files',
	        cadTool: 'xpedition',
	        requiredFileExt,
	        triggeringFile,
	        errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.MissingRequiredFile,
	    };
	}
	function checkXpeditionRequiredFiles(context) {
	    const paths = context.files.map((file) => file.relativePath.toLowerCase());
	    const triggeringFile = context.fileDetails.find((detail) => detail.cadTool === 'xpedition')?.relativePath ??
	        context.files[0]?.relativePath ??
	        '';
	    const findings = [];
	    if (!paths.some((path) => path.endsWith(PROJECT_EXT))) {
	        findings.push(missingFinding(PROJECT_EXT, 'a .prj project file', triggeringFile));
	    }
	    if (!paths.some((path) => NETLIST_EXTS.some((ext) => path.endsWith(ext)))) {
	        findings.push(missingFinding(NETLIST_DISPLAY_EXT, 'a .tel or .qcv netlist', triggeringFile));
	    }
	    return findings;
	}
	
	return xpeditionRequiredFiles;
}

var hasRequiredRequiredFiles;

function requireRequiredFiles () {
	if (hasRequiredRequiredFiles) return requiredFiles;
	hasRequiredRequiredFiles = 1;
	Object.defineProperty(requiredFiles, "__esModule", { value: true });
	requiredFiles.checkRequiredFilesForTool = checkRequiredFilesForTool;
	const altiumRequiredFiles_1 = requireAltiumRequiredFiles();
	const orcadRequiredFiles_1 = requireOrcadRequiredFiles();
	const xpeditionRequiredFiles_1 = requireXpeditionRequiredFiles();
	function checkRequiredFilesForTool(tool, context) {
	    switch (tool) {
	        case 'altium':
	            return (0, altiumRequiredFiles_1.checkAltiumRequiredFiles)(context);
	        case 'orcad':
	            return (0, orcadRequiredFiles_1.checkOrcadRequiredFiles)(context);
	        case 'xpedition':
	            return (0, xpeditionRequiredFiles_1.checkXpeditionRequiredFiles)(context);
	        default:
	            return [];
	    }
	}
	
	return requiredFiles;
}

var fileSecurity = {};

var hasRequiredFileSecurity;

function requireFileSecurity () {
	if (hasRequiredFileSecurity) return fileSecurity;
	hasRequiredFileSecurity = 1;
	Object.defineProperty(fileSecurity, "__esModule", { value: true });
	fileSecurity.validateFilenameSecurity = validateFilenameSecurity;
	fileSecurity.getBlockedExtensions = getBlockedExtensions;
	const BLOCKED_EXTENSIONS = [
	    '.exe',
	    '.dll',
	    '.so',
	    '.dylib',
	    '.app',
	    '.bat',
	    '.cmd',
	    '.com',
	    '.msi',
	    '.scr',
	    '.js',
	    '.vbs',
	    '.vbe',
	    '.wsf',
	    '.wsh',
	    '.ps1',
	    '.psm1',
	    '.psd1',
	    '.sh',
	    '.bash',
	    '.zsh',
	    '.fish',
	    '.git',
	    '.svn',
	    '.hg',
	    '.DS_Store',
	    '.gitignore',
	    '.gitattributes',
	    '.jar',
	    '.war',
	    '.ear',
	    '.reg',
	    '.inf',
	    '.cpl',
	    '.scf',
	    '.lnk',
	    '.url',
	    '.desktop',
	];
	const DANGEROUS_PATTERNS = [
	    /\.\./,
	    /^\.+$/,
	    /[\x00-\x1f]/,
	    /[<>:"|?*]/,
	];
	function validateFilenameSecurity(filename) {
	    if (!filename || filename.trim().length === 0) {
	        return { isValid: false, reason: 'Filename is empty' };
	    }
	    const normalizedFilename = filename.toLowerCase().trim();
	    for (const ext of BLOCKED_EXTENSIONS) {
	        if (normalizedFilename.endsWith(ext.toLowerCase())) {
	            return { isValid: false, reason: `File type ${ext} is not allowed for security reasons` };
	        }
	    }
	    for (const pattern of DANGEROUS_PATTERNS) {
	        if (pattern.test(filename)) {
	            return { isValid: false, reason: 'Filename contains invalid characters or patterns' };
	        }
	    }
	    if (filename.length > 255) {
	        return { isValid: false, reason: 'Filename is too long (max 255 characters)' };
	    }
	    return { isValid: true };
	}
	function getBlockedExtensions() {
	    return BLOCKED_EXTENSIONS;
	}
	
	return fileSecurity;
}

var projectArchive = {};

var hasRequiredProjectArchive;

function requireProjectArchive () {
	if (hasRequiredProjectArchive) return projectArchive;
	hasRequiredProjectArchive = 1;
	Object.defineProperty(projectArchive, "__esModule", { value: true });
	projectArchive.ArchiveExpansionError = projectArchive.ArchiveExpansionFailure = projectArchive.ArchiveEntryRejection = void 0;
	projectArchive.expandProjectArchive = expandProjectArchive;
	projectArchive.firstExceededBound = firstExceededBound;
	const fflate_1 = /*@__PURE__*/ requireBrowser();
	const sigma_engine_shared_1 = requireDist$1();
	var ArchiveEntryRejection;
	(function (ArchiveEntryRejection) {
	    ArchiveEntryRejection["UnsafePath"] = "unsafe_path";
	    ArchiveEntryRejection["TooLarge"] = "too_large";
	})(ArchiveEntryRejection || (projectArchive.ArchiveEntryRejection = ArchiveEntryRejection = {}));
	var ArchiveExpansionFailure;
	(function (ArchiveExpansionFailure) {
	    ArchiveExpansionFailure["Unreadable"] = "unreadable";
	    ArchiveExpansionFailure["TooManyEntries"] = "too_many_entries";
	    ArchiveExpansionFailure["ExpansionTooLarge"] = "expansion_too_large";
	    ArchiveExpansionFailure["CompressionRatioTooHigh"] = "compression_ratio_too_high";
	})(ArchiveExpansionFailure || (projectArchive.ArchiveExpansionFailure = ArchiveExpansionFailure = {}));
	class ArchiveExpansionError extends Error {
	    constructor(failure, message) {
	        super(message);
	        this.name = 'ArchiveExpansionError';
	        this.failure = failure;
	    }
	}
	projectArchive.ArchiveExpansionError = ArchiveExpansionError;
	async function expandProjectArchive(data, tool) {
	    const rejected = [];
	    const failures = [];
	    let acceptedCount = 0;
	    let expandedBytes = 0;
	    const acceptEntry = (info) => {
	        const path = normalizeEntryPath(info.name);
	        if (path === '' || path.endsWith('/')) {
	            return false;
	        }
	        if (isUnsafeEntryPath(path)) {
	            rejected.push({ path: info.name, reason: ArchiveEntryRejection.UnsafePath });
	            return false;
	        }
	        if (hasBlockedSegment(path) || sigma_engine_shared_1.Files.isToolGeneratedArtifact(path, tool)) {
	            return false;
	        }
	        if (info.originalSize > sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES) {
	            rejected.push({ path, reason: ArchiveEntryRejection.TooLarge });
	            return false;
	        }
	        acceptedCount += 1;
	        expandedBytes += info.originalSize;
	        const breach = firstExceededBound({
	            entryCount: acceptedCount,
	            expandedBytes,
	            storedBytes: data.length,
	        });
	        if (breach !== null) {
	            failures.push(breach);
	            return false;
	        }
	        return true;
	    };
	    const unzipped = await new Promise((resolve, reject) => {
	        (0, fflate_1.unzip)(data, { filter: acceptEntry }, (error, result) => {
	            if (error) {
	                reject(new ArchiveExpansionError(ArchiveExpansionFailure.Unreadable, error.message));
	                return;
	            }
	            resolve(result);
	        });
	    });
	    const failure = failures[0];
	    if (failure !== undefined) {
	        throw new ArchiveExpansionError(failure, `Archive expansion exceeded its limits (${failure})`);
	    }
	    const names = Object.keys(unzipped);
	    const wrapper = findWrapperDirectory(names.map(normalizeEntryPath));
	    const members = [];
	    for (const name of names) {
	        const contents = unzipped[name];
	        if (contents === undefined) {
	            continue;
	        }
	        const path = normalizeEntryPath(name);
	        members.push({ path: wrapper === null ? path : path.slice(wrapper.length), contents });
	    }
	    return { members, rejected };
	}
	function firstExceededBound(declared) {
	    if (declared.entryCount > sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_FILES) {
	        return ArchiveExpansionFailure.TooManyEntries;
	    }
	    if (declared.expandedBytes > sigma_engine_shared_1.Files.ARCHIVE_LIMITS.MAX_EXPANDED_BYTES) {
	        return ArchiveExpansionFailure.ExpansionTooLarge;
	    }
	    if (declared.expandedBytes > declared.storedBytes * sigma_engine_shared_1.Files.ARCHIVE_LIMITS.MAX_COMPRESSION_RATIO) {
	        return ArchiveExpansionFailure.CompressionRatioTooHigh;
	    }
	    return null;
	}
	function normalizeEntryPath(name) {
	    return name.replace(/\\/g, '/').replace(/^\.\//, '');
	}
	function isUnsafeEntryPath(path) {
	    return path.startsWith('/') || /^[a-z]:/i.test(path) || path.split('/').includes('..');
	}
	function hasBlockedSegment(path) {
	    const blocked = sigma_engine_shared_1.Files.BLOCKED_DIRECTORIES;
	    return path.split('/').some((segment) => blocked.includes(segment));
	}
	function findWrapperDirectory(paths) {
	    const first = paths[0];
	    if (first === undefined) {
	        return null;
	    }
	    const slash = first.indexOf('/');
	    if (slash < 0) {
	        return null;
	    }
	    const prefix = first.slice(0, slash + 1);
	    return paths.every((path) => path.startsWith(prefix)) ? prefix : null;
	}
	
	return projectArchive;
}

var build$1 = {};

var hasRequiredBuild$1;

function requireBuild$1 () {
	if (hasRequiredBuild$1) return build$1;
	hasRequiredBuild$1 = 1;
	Object.defineProperty(build$1, "__esModule", { value: true });
	build$1.serializeBuildProjectDefinition = serializeBuildProjectDefinition;
	build$1.serializeObjectBlobsToString = serializeObjectBlobsToString;
	async function serializeBuildProjectDefinition(project, serializeObjectBlobs = serializeObjectBlobsToString) {
	    return serializeObjectBlobs(project);
	}
	async function serializeObjectBlobsToString(obj, blobToBase64 = blobToBase64Browser) {
	    const result = {};
	    for (const key in obj) {
	        const value = obj[key];
	        if (value instanceof Blob) {
	            result[key] = `base64blob:${await blobToBase64(value)}`;
	        }
	        else if (!value) {
	            result[key] = value;
	        }
	        else if (typeof value === 'object') {
	            result[key] = await serializeObjectBlobsToString(value, blobToBase64);
	        }
	        else {
	            result[key] = value;
	        }
	    }
	    return result;
	}
	function blobToBase64Browser(value) {
	    return new Promise((resolve, reject) => {
	        const reader = new FileReader();
	        reader.onload = () => resolve(reader.result);
	        reader.onerror = reject;
	        reader.readAsDataURL(value);
	    });
	}
	
	return build$1;
}

var projectPipeline = {};

var ProjectFileCollectionValidator = {};

var hasRequiredProjectFileCollectionValidator;

function requireProjectFileCollectionValidator () {
	if (hasRequiredProjectFileCollectionValidator) return ProjectFileCollectionValidator;
	hasRequiredProjectFileCollectionValidator = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ProjectFileCollectionValidator = exports.TYPE_DETECTION_EXCLUDED_SEMANTIC_TYPES = exports.TYPE_DETECTION_SEMANTIC_TYPES = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const sigma_engine_shared_2 = requireDist$1();
		const required_files_1 = requireRequiredFiles();
		const validation_1 = requireValidation();
		const FILE_VALIDATION_CONCURRENCY = 20;
		exports.TYPE_DETECTION_SEMANTIC_TYPES = new Set([
		    'project',
		    'board',
		    'schematic',
		]);
		exports.TYPE_DETECTION_EXCLUDED_SEMANTIC_TYPES = new Set(['netlist']);
		function toForwardSlash(p) {
		    return p.replace(/\\/g, '/');
		}
		function normalizeForComparison(p) {
		    return sigma_engine_shared_1.Files.normalizePath(toForwardSlash(p)).toLowerCase();
		}
		async function settleWithConcurrency(tasks, concurrency) {
		    const results = new Array(tasks.length);
		    let next = 0;
		    async function consume() {
		        while (next < tasks.length) {
		            const i = next++;
		            try {
		                results[i] = { status: 'fulfilled', value: await tasks[i]() };
		            }
		            catch (reason) {
		                results[i] = { status: 'rejected', reason };
		            }
		        }
		    }
		    await Promise.all(Array.from({ length: Math.min(concurrency, tasks.length) }, () => consume()));
		    return results;
		}
		function buildCacheKey(file) {
		    const f = file.contents;
		    const lastModified = typeof f.lastModified === 'number' ? f.lastModified : 0;
		    return `${file.relativePath}|${file.contents.size}|${lastModified}`;
		}
		class ProjectFileCollectionValidator {
		    constructor() {
		        this.fileValidationCache = new Map();
		    }
		    async validate(files, options = {}) {
		        const innerResults = [];
		        this.validateMetadata(files, innerResults);
		        const fileDetails = await this.validateFileContents(files, innerResults, options.declaredTool);
		        const projectType = this.detectProjectType(fileDetails, innerResults, options.declaredTool);
		        const missingRefs = this.checkReferencedFiles(files, fileDetails, innerResults);
		        const requiredFileResults = this.checkRequiredFiles(projectType, files, fileDetails, missingRefs, innerResults);
		        const detected = projectType ? this.detectProjects(projectType, files, fileDetails, innerResults) : undefined;
		        const summary = this.buildSummary(fileDetails, missingRefs, requiredFileResults);
		        const result = {
		            type: 'file-collection-validation',
		            message: 'File collection validation complete',
		            innerResults,
		            projectType,
		            projectDefinition: detected?.projectDefinition,
		            projects: detected?.projects,
		            summary,
		        };
		        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(result);
		        return result;
		    }
		    validateMetadata(files, innerResults) {
		        if (files.length > sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_FILES) {
		            innerResults.push({
		                type: 'metadata-validation',
		                message: `Too many files: ${files.length} exceeds maximum of ${sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_FILES}`,
		                severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                relativePath: '',
		                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.TooManyFiles,
		            });
		        }
		        const totalSize = files.reduce((sum, f) => sum + f.contents.size, 0);
		        if (totalSize > sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_TOTAL_BYTES) {
		            innerResults.push({
		                type: 'metadata-validation',
		                message: `Total size ${totalSize} bytes exceeds maximum of ${sigma_engine_shared_1.Files.UPLOAD_LIMITS.MAX_TOTAL_BYTES / (1024 * 1024 * 1024)} GB`,
		                severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                relativePath: '',
		                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.TotalSizeExceeded,
		            });
		        }
		        for (const file of files) {
		            const errors = sigma_engine_shared_1.Files.validateFileMetadata({
		                relativePath: file.relativePath,
		                size: file.contents.size,
		            });
		            for (const error of errors) {
		                const errorType = this.mapValidationErrorCode(error.code);
		                innerResults.push({
		                    type: 'metadata-validation',
		                    message: error.message,
		                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                    relativePath: file.relativePath,
		                    errorType,
		                });
		            }
		        }
		    }
		    mapValidationErrorCode(code) {
		        switch (code) {
		            case 'FILE_TOO_LARGE':
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.FileTooLarge;
		            case 'PATH_TRAVERSAL':
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.PathTraversal;
		            case 'PATH_TOO_DEEP':
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.PathTooDeep;
		            case 'PATH_TOO_LONG':
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.PathTooLong;
		            case 'INVALID_FILENAME':
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.InvalidFilename;
		            case 'BLOCKED_EXTENSION':
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.BlockedExtension;
		            default:
		                return sigma_engine_shared_2.FileCollectionValidationErrorType.InvalidFilename;
		        }
		    }
		    async validateFileContents(files, innerResults, declaredTool) {
		        const currentKeys = new Set();
		        const fileKeyPairs = files.map((file) => {
		            const key = buildCacheKey(file);
		            currentKeys.add(key);
		            return { file, key };
		        });
		        for (const existingKey of this.fileValidationCache.keys()) {
		            if (!currentKeys.has(existingKey)) {
		                this.fileValidationCache.delete(existingKey);
		            }
		        }
		        const settled = await settleWithConcurrency(fileKeyPairs.map(({ file, key }) => async () => {
		            const cached = this.fileValidationCache.get(key);
		            if (cached) {
		                return { file, result: cached };
		            }
		            const result = await (0, validation_1.validateFile)(sigma_engine_shared_1.Files.basenamePath(toForwardSlash(file.relativePath)), file.contents);
		            this.fileValidationCache.set(key, result);
		            return { file, result };
		        }), FILE_VALIDATION_CONCURRENCY);
		        const fileDetails = [];
		        for (const outcome of settled) {
		            if (outcome.status === 'rejected') {
		                fileDetails.push({
		                    type: 'file-validation',
		                    message: `Validation failed: ${outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason)}`,
		                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                    relativePath: '',
		                    size: 0,
		                });
		                continue;
		            }
		            const { file, result } = outcome.value;
		            const detail = {
		                type: 'file-validation',
		                relativePath: file.relativePath,
		                size: file.contents.size,
		                fileValidationResult: result,
		                message: '',
		                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
		            };
		            if (result.isValid) {
		                detail.semanticType = result.data.semanticType;
		                detail.cadTool = result.data.fileFormat.cadTool;
		                detail.referencedFiles = result.data.referencedFiles;
		                detail.message = `${sigma_engine_shared_1.Files.basenamePath(toForwardSlash(file.relativePath))}: ${result.data.semanticType} (${result.data.fileFormat.cadTool})`;
		            }
		            else {
		                const isUnrecognized = result.errors.some((e) => e.startsWith('Unrecognized file type'));
		                detail.semanticType = result.semanticType;
		                const isForeignToDeclaredTool = declaredTool !== undefined &&
		                    result.claimedTools !== undefined &&
		                    result.claimedTools.length > 0 &&
		                    !result.claimedTools.includes(declaredTool);
		                detail.message = result.errors.join('; ');
		                if (isUnrecognized) {
		                    detail.severity = sigma_engine_shared_1.BoundaryLevel.Warning;
		                    detail.errorType = sigma_engine_shared_2.FileCollectionValidationErrorType.UnrecognizedFileType;
		                }
		                else if (isForeignToDeclaredTool) {
		                    detail.severity = sigma_engine_shared_1.BoundaryLevel.Warning;
		                    detail.errorType = sigma_engine_shared_2.FileCollectionValidationErrorType.ForeignToolFile;
		                    detail.message = `${detail.message} (not part of the declared ${declaredTool} project)`;
		                }
		                else {
		                    detail.severity = sigma_engine_shared_1.BoundaryLevel.Error;
		                    detail.errorType = sigma_engine_shared_2.FileCollectionValidationErrorType.FileValidationFailed;
		                }
		            }
		            fileDetails.push(detail);
		        }
		        innerResults.push(...fileDetails);
		        return fileDetails;
		    }
		    detectProjectType(fileDetails, innerResults, declaredTool) {
		        const qualifyingTools = new Set();
		        let hasExcludedNetlists = false;
		        for (const detail of fileDetails) {
		            if (!detail.semanticType || !detail.cadTool) {
		                continue;
		            }
		            if (exports.TYPE_DETECTION_EXCLUDED_SEMANTIC_TYPES.has(detail.semanticType)) {
		                hasExcludedNetlists = true;
		                continue;
		            }
		            if (exports.TYPE_DETECTION_SEMANTIC_TYPES.has(detail.semanticType)) {
		                qualifyingTools.add(detail.cadTool);
		            }
		        }
		        const detectedTypes = Array.from(qualifyingTools);
		        if (detectedTypes.length === 0) {
		            innerResults.push({
		                type: 'project-type-detection',
		                message: 'No recognized project type could be determined. No files were uploaded.',
		                severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                detectedTypes: [],
		            });
		            return undefined;
		        }
		        let selectedType;
		        if (detectedTypes.length > 1) {
		            if (declaredTool === undefined || !qualifyingTools.has(declaredTool)) {
		                innerResults.push({
		                    type: 'project-type-detection',
		                    message: `Multiple project types detected: ${detectedTypes.join(', ')}. Please upload files from a single CAD tool.`,
		                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                    detectedTypes,
		                });
		                return undefined;
		            }
		            selectedType = declaredTool;
		            innerResults.push({
		                type: 'project-type-detection',
		                message: `Multiple project types detected (${detectedTypes.join(', ')}). Using the declared project type: ${declaredTool}.`,
		                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
		                detectedTypes,
		                selectedType,
		            });
		        }
		        else {
		            selectedType = detectedTypes[0];
		            innerResults.push({
		                type: 'project-type-detection',
		                message: `Detected project type: ${selectedType}`,
		                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
		                detectedTypes,
		                selectedType,
		            });
		        }
		        if (hasExcludedNetlists) {
		            innerResults.push({
		                type: 'project-type-detection',
		                message: 'Netlist files were present but excluded from project type detection (netlists can originate from any CAD tool)',
		                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
		                detectedTypes: [],
		            });
		        }
		        return selectedType;
		    }
		    checkReferencedFiles(files, fileDetails, innerResults) {
		        const normalizedPaths = new Set(files.map((f) => normalizeForComparison(f.relativePath)));
		        const normalizedBasenames = new Set(files.map((f) => sigma_engine_shared_1.Files.basenamePath(normalizeForComparison(f.relativePath))));
		        const missingFiles = [];
		        for (const detail of fileDetails) {
		            if (!detail.referencedFiles?.length) {
		                continue;
		            }
		            const fileDir = sigma_engine_shared_1.Files.dirnamePath(normalizeForComparison(detail.relativePath));
		            for (const ref of detail.referencedFiles) {
		                const normalizedRef = normalizeForComparison(ref);
		                const refBasename = sigma_engine_shared_1.Files.basenamePath(normalizedRef);
		                if (normalizedPaths.has(normalizedRef)) {
		                    continue;
		                }
		                const resolved = normalizeForComparison(sigma_engine_shared_1.Files.joinPath(fileDir, normalizedRef));
		                if (normalizedPaths.has(resolved)) {
		                    continue;
		                }
		                if (normalizedBasenames.has(refBasename)) {
		                    continue;
		                }
		                if (!missingFiles.includes(ref)) {
		                    missingFiles.push(ref);
		                }
		                innerResults.push({
		                    type: 'referenced-file-check',
		                    message: `File "${detail.relativePath}" references "${ref}" which is not in the uploaded files`,
		                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
		                    referencingFile: detail.relativePath,
		                    missingFile: ref,
		                });
		            }
		        }
		        return missingFiles;
		    }
		    checkRequiredFiles(projectType, files, fileDetails, missingReferencedFiles, innerResults) {
		        if (!projectType) {
		            return [];
		        }
		        const results = (0, required_files_1.checkRequiredFilesForTool)(projectType, { files, fileDetails, missingReferencedFiles });
		        innerResults.push(...results);
		        return results;
		    }
		    detectProjects(projectType, files, fileDetails, innerResults) {
		        const detection = (0, validation_1.detectProjectsForTool)(projectType, files, fileDetails);
		        for (const warning of detection.warnings) {
		            innerResults.push({
		                type: 'project-type-detection',
		                message: warning,
		                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
		                detectedTypes: [projectType],
		                selectedType: projectType,
		            });
		        }
		        if (detection.projects.length === 0) {
		            innerResults.push({
		                type: 'project-type-detection',
		                message: `No ${projectType} project could be assembled from the uploaded files.`,
		                severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                detectedTypes: [projectType],
		                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.MultipleProjectsDetected,
		            });
		            return { projectDefinition: undefined, projects: detection.projects };
		        }
		        if (detection.projects.length > 1) {
		            const projectNames = detection.projects.map((p) => p.name ?? '(unnamed)');
		            innerResults.push({
		                type: 'project-type-detection',
		                message: `Multiple ${projectType} projects detected (${projectNames.join(', ')}). Please upload files from a single project.`,
		                severity: sigma_engine_shared_1.BoundaryLevel.Error,
		                detectedTypes: [projectType],
		                errorType: sigma_engine_shared_2.FileCollectionValidationErrorType.MultipleProjectsDetected,
		            });
		            return { projectDefinition: undefined, projects: detection.projects };
		        }
		        return { projectDefinition: detection.projects[0], projects: detection.projects };
		    }
		    buildSummary(fileDetails, missingRefs, requiredFileResults) {
		        let validFiles = 0;
		        let invalidFiles = 0;
		        let boards = 0;
		        let schematics = 0;
		        let projectFiles = 0;
		        let otherRecognized = 0;
		        for (const detail of fileDetails) {
		            if (detail.severity === sigma_engine_shared_1.BoundaryLevel.Error) {
		                invalidFiles++;
		            }
		            else {
		                validFiles++;
		                switch (detail.semanticType) {
		                    case 'board':
		                        boards++;
		                        break;
		                    case 'schematic':
		                        schematics++;
		                        break;
		                    case 'project':
		                        projectFiles++;
		                        break;
		                    default:
		                        if (detail.semanticType) {
		                            otherRecognized++;
		                        }
		                        break;
		                }
		            }
		        }
		        return {
		            totalFiles: fileDetails.length,
		            validFiles,
		            invalidFiles,
		            boards,
		            schematics,
		            projectFiles,
		            otherRecognized,
		            missingReferencedFiles: missingRefs,
		            missingRequiredFiles: [
		                ...new Set(requiredFileResults
		                    .filter((r) => r.type === 'required-file-check' && r.severity === sigma_engine_shared_1.BoundaryLevel.Error)
		                    .map((r) => r.requiredFileExt)),
		            ],
		        };
		    }
		}
		exports.ProjectFileCollectionValidator = ProjectFileCollectionValidator;
		
	} (ProjectFileCollectionValidator));
	return ProjectFileCollectionValidator;
}

var hasRequiredProjectPipeline;

function requireProjectPipeline () {
	if (hasRequiredProjectPipeline) return projectPipeline;
	hasRequiredProjectPipeline = 1;
	Object.defineProperty(projectPipeline, "__esModule", { value: true });
	projectPipeline.validateFiles = validateFiles;
	projectPipeline.detectCadTool = detectCadTool;
	projectPipeline.buildProjectDefinition = buildProjectDefinition;
	projectPipeline.parseNetlist = parseNetlist;
	const ProjectFileCollectionValidator_1 = requireProjectFileCollectionValidator();
	const validation_1 = requireValidation();
	const kicadNetlistValidation_1 = requireKicadNetlistValidation();
	const PCAD_1 = requirePCAD();
	const OrCAD_1 = requireOrCAD();
	const Allegro_1 = requireAllegro();
	const Util_1 = requireUtil();
	async function validateFiles(files) {
	    const results = await Promise.all(files.map(async ({ name, blob }) => {
	        const validationResult = await (0, validation_1.validateFile)(name, blob);
	        return { filename: name, blob, validationResult };
	    }));
	    return {
	        validFiles: results.filter((r) => r.validationResult.isValid),
	        invalidFiles: results.filter((r) => !r.validationResult.isValid),
	    };
	}
	function detectCadTool(validFiles, declaredTool) {
	    const cadTools = new Set();
	    for (const { validationResult } of validFiles) {
	        if (!validationResult.isValid || !ProjectFileCollectionValidator_1.TYPE_DETECTION_SEMANTIC_TYPES.has(validationResult.data.semanticType)) {
	            continue;
	        }
	        if (validationResult.data.fileFormat.cadTool) {
	            cadTools.add(validationResult.data.fileFormat.cadTool);
	        }
	    }
	    if (cadTools.size === 0) {
	        throw new Error('Could not detect CAD tool from uploaded files');
	    }
	    if (cadTools.size > 1) {
	        if (declaredTool !== undefined && cadTools.has(declaredTool)) {
	            return declaredTool;
	        }
	        throw new Error(`Multiple CAD tools detected: ${Array.from(cadTools).join(', ')}`);
	    }
	    return Array.from(cadTools)[0];
	}
	function buildProjectDefinition(validFiles, cadTool, getEntryName) {
	    const projectDefinition = {
	        type: cadTool,
	        boards: {},
	        schematics: {},
	        other: {},
	    };
	    for (const { filename, blob, validationResult } of validFiles) {
	        if (!validationResult.isValid) {
	            continue;
	        }
	        const entryName = getEntryName ? getEntryName(filename) : filename;
	        const semanticType = validationResult.data.semanticType;
	        const fileDescriptor = {
	            type: semanticType,
	            originalFileName: entryName,
	            contents: blob,
	            validationResult,
	        };
	        if (semanticType === 'board') {
	            projectDefinition.boards[filename] = fileDescriptor;
	        }
	        else if (semanticType === 'schematic') {
	            projectDefinition.schematics[entryName] = fileDescriptor;
	        }
	        else {
	            projectDefinition.other[entryName] = fileDescriptor;
	        }
	    }
	    return projectDefinition;
	}
	async function parseNetlist(filename, netlistBytes) {
	    const utf8Content = new TextDecoder('utf-8').decode(netlistBytes);
	    const kicadResult = (0, kicadNetlistValidation_1.validateKicadNetlist)(filename, utf8Content);
	    const kicadSchematic = (0, kicadNetlistValidation_1.getSchematicFromResult)(kicadResult);
	    if (kicadSchematic) {
	        return kicadSchematic;
	    }
	    try {
	        const pcadContent = (0, Util_1.decodeWindows1252Clean)(netlistBytes);
	        if ((0, PCAD_1.validatePcadFile)(filename, pcadContent).isValid) {
	            const pcadResult = (0, PCAD_1.parsePcadNetlist)(pcadContent);
	            if (pcadResult.schematic) {
	                return pcadResult.schematic;
	            }
	        }
	    }
	    catch {
	    }
	    const orcadResult = await (0, OrCAD_1.validateOrCADXFile)(filename, utf8Content);
	    if (orcadResult.isValid) {
	        const orcadParseResult = (0, OrCAD_1.parseOrCADNetlist)(utf8Content);
	        const orcadSchematic = (0, OrCAD_1.getSchematicFromOrcadParseResult)(orcadParseResult);
	        if (orcadSchematic) {
	            return orcadSchematic.schematic;
	        }
	    }
	    const allegroResult = (0, Allegro_1.validateAllegroFile)(filename, utf8Content);
	    if (allegroResult.isValid && allegroResult.data?.semanticType === 'netlist') {
	        const parseResult = allegroResult.data.metadata[Allegro_1.AllegroProjectMetadataKey.ParsedNetlist];
	        const allegroSchematic = parseResult ? (0, Allegro_1.getSchematicFromAllegroParseResult)(parseResult) : undefined;
	        if (allegroSchematic) {
	            return allegroSchematic.schematic;
	        }
	    }
	    return undefined;
	}
	
	return projectPipeline;
}

var ExtractedAsset = {};

var hasRequiredExtractedAsset;

function requireExtractedAsset () {
	if (hasRequiredExtractedAsset) return ExtractedAsset;
	hasRequiredExtractedAsset = 1;
	Object.defineProperty(ExtractedAsset, "__esModule", { value: true });
	
	return ExtractedAsset;
}

var ProjectModelUtil = {};

var hasRequiredProjectModelUtil;

function requireProjectModelUtil () {
	if (hasRequiredProjectModelUtil) return ProjectModelUtil;
	hasRequiredProjectModelUtil = 1;
	Object.defineProperty(ProjectModelUtil, "__esModule", { value: true });
	ProjectModelUtil.ProjectModelUtil = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	class ProjectModelUtil$1 {
	    constructor(projectModel) {
	        this.projectModel = projectModel;
	    }
	    getModulePinPosition(moduleRef, pinRef) {
	        const [design, module] = this.getDesignModuleByRef(moduleRef);
	        if (design.modelType !== 'schematic') {
	            throw new Error(`Module ${moduleRef} is not in a schematic design`);
	        }
	        const component = this.projectModel.components[module.componentRef];
	        if (!component) {
	            throw new Error(`Component ${module.componentRef} not found for module ${module.name}`);
	        }
	        const footprint = component[design.modelType === 'schematic' ? 'symbols' : 'footprints'].find((f) => f.ref === module.footprintRef);
	        if (!footprint) {
	            throw new Error(`${design.modelType === 'schematic' ? 'Symbol' : 'Footprint'} ${module.footprintRef} not found for module ${module.name}`);
	        }
	        if ('pins' in footprint) {
	            const pin = footprint.pins.find((p) => p.ref === pinRef);
	            if (!pin) {
	                throw new Error(`Pin ${pinRef} not found in symbol ${footprint.ref}`);
	            }
	            let transformedStart = sigma_engine_shared_1.Utils.Transform.rotatePoint(pin.start, -(module.transform.angle ?? 0));
	            let transformedEnd = sigma_engine_shared_1.Utils.Transform.rotatePoint(pin.end, -(module.transform.angle ?? 0));
	            if (module.transform.mirrored) {
	                transformedStart = sigma_engine_shared_1.Utils.Transform.getXyWithMirror(transformedStart, module.transform.mirrored);
	                transformedEnd = sigma_engine_shared_1.Utils.Transform.getXyWithMirror(transformedEnd, module.transform.mirrored);
	            }
	            const center = sigma_engine_shared_1.Utils.Transform.getXyWithMirror(sigma_engine_shared_1.Utils.Transform.rotatePoint([0, 0], -(module.transform.angle ?? 0), module.transform.pos ?? [0, 0]), module.transform.mirrored);
	            const offset = [center[0] + (module.center?.[0] ?? 0), center[1] + (module.center?.[1] ?? 0)];
	            transformedStart = sigma_engine_shared_1.Utils.Transform.translatePoint(transformedStart, offset);
	            transformedEnd = sigma_engine_shared_1.Utils.Transform.translatePoint(transformedEnd, offset);
	            return {
	                start: transformedStart,
	                end: transformedEnd,
	            };
	        }
	        else {
	            throw new Error(`Footprint ${footprint.ref} is not a schematic footprint`);
	        }
	    }
	    getComponentSymbolByRef(componentRef, footprintRef) {
	        const component = this.projectModel.components[componentRef];
	        if (!component) {
	            throw new Error(`Component ${componentRef} not found`);
	        }
	        const symbol = component.symbols.find((s) => s.ref === footprintRef);
	        if (!symbol) {
	            throw new Error(`Symbol ${footprintRef} not found for component ${component.name}`);
	        }
	        return { component, symbol };
	    }
	    getDesignModuleByRef(moduleRef) {
	        for (const design of Object.values(this.projectModel.designs)) {
	            const module = design.modules.find((m) => m.ref === moduleRef);
	            if (module) {
	                return [design, module];
	            }
	        }
	        throw new Error(`Module ${moduleRef} not found in any design`);
	    }
	}
	ProjectModelUtil.ProjectModelUtil = ProjectModelUtil$1;
	
	return ProjectModelUtil;
}

var hasRequiredParse;

function requireParse () {
	if (hasRequiredParse) return Parse;
	hasRequiredParse = 1;
	(function (exports) {
		var __createBinding = (Parse && Parse.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Parse && Parse.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ProjectFileCollectionValidator = exports.Constants = exports.Area = exports.AltiumFileType = exports.validateAltiumFile = exports.getAltiumFileType = exports.validateKicadFile = exports.getKicadFileType = exports.KicadFileType = exports.Validation = exports.Util = exports.parseDru = exports.PCAD = exports.Allegro = exports.OrCAD = exports.Eagle = exports.Altium = exports.KiCad = void 0;
		const KiCad = requireKicad();
		exports.KiCad = KiCad;
		const Eagle = requireEagle();
		exports.Eagle = Eagle;
		const Altium = requireAltium();
		exports.Altium = Altium;
		const OrCAD = requireOrCAD();
		exports.OrCAD = OrCAD;
		const Allegro = requireAllegro();
		exports.Allegro = Allegro;
		const PCAD = requirePCAD();
		exports.PCAD = PCAD;
		const dru_1 = requireDru();
		Object.defineProperty(exports, "parseDru", { enumerable: true, get: function () { return dru_1.parseDru; } });
		const Util = requireUtil();
		exports.Util = Util;
		const Validation = requireModelValidation();
		exports.Validation = Validation;
		__exportStar(requireValidation(), exports);
		__exportStar(requireRequiredFiles(), exports);
		__exportStar(requireFileSecurity(), exports);
		__exportStar(requireProjectArchive(), exports);
		var kicad_file_type_1 = requireKicadFileType();
		Object.defineProperty(exports, "KicadFileType", { enumerable: true, get: function () { return kicad_file_type_1.KicadFileType; } });
		var kicadValidation_1 = requireKicadValidation();
		Object.defineProperty(exports, "getKicadFileType", { enumerable: true, get: function () { return kicadValidation_1.getKicadFileType; } });
		Object.defineProperty(exports, "validateKicadFile", { enumerable: true, get: function () { return kicadValidation_1.validateKicadFile; } });
		var altiumValidation_1 = requireAltiumValidation();
		Object.defineProperty(exports, "getAltiumFileType", { enumerable: true, get: function () { return altiumValidation_1.getAltiumFileType; } });
		Object.defineProperty(exports, "validateAltiumFile", { enumerable: true, get: function () { return altiumValidation_1.validateAltiumFile; } });
		var altiumFileTypes_1 = requireAltiumFileTypes();
		Object.defineProperty(exports, "AltiumFileType", { enumerable: true, get: function () { return altiumFileTypes_1.AltiumFileType; } });
		const Area_1 = requireArea();
		exports.Area = Area_1.default;
		exports.Constants = requireConstants$5();
		__exportStar(requireBuild$1(), exports);
		__exportStar(requireProjectPipeline(), exports);
		__exportStar(requireFonts(), exports);
		__exportStar(requireExtractedAsset(), exports);
		__exportStar(requireProjectModelUtil(), exports);
		var ProjectFileCollectionValidator_1 = requireProjectFileCollectionValidator();
		Object.defineProperty(exports, "ProjectFileCollectionValidator", { enumerable: true, get: function () { return ProjectFileCollectionValidator_1.ProjectFileCollectionValidator; } });
		
	} (Parse));
	return Parse;
}

var ParentDeployment = {};

var packageCatalogueLookup = {};

var hasRequiredPackageCatalogueLookup;

function requirePackageCatalogueLookup () {
	if (hasRequiredPackageCatalogueLookup) return packageCatalogueLookup;
	hasRequiredPackageCatalogueLookup = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PackageCatalogueLookupResponseSchema = exports.PackageCatalogueLookupResultSchema = exports.PackageCatalogueSummarySchema = exports.PackageCatalogueLookupRequestSchema = exports.PACKAGE_CATALOGUE_LOOKUP_MAX_MPNS = exports.PACKAGE_CATALOGUE_SCHEMA_VERSION = void 0;
		const z = /*@__PURE__*/ requireZod();
		exports.PACKAGE_CATALOGUE_SCHEMA_VERSION = 1;
		exports.PACKAGE_CATALOGUE_LOOKUP_MAX_MPNS = 100;
		exports.PackageCatalogueLookupRequestSchema = z.object({
		    mpns: z.array(z.string().trim().min(1).max(256)).min(1).max(exports.PACKAGE_CATALOGUE_LOOKUP_MAX_MPNS),
		});
		exports.PackageCatalogueSummarySchema = z.object({
		    id: z.string().min(1),
		    manufacturerPartNumber: z.string(),
		    variantSuffix: z.string(),
		    manufacturerName: z.string(),
		    packageFamilyName: z.string(),
		    datasheetUrl: z.string().nullable(),
		});
		exports.PackageCatalogueLookupResultSchema = z.object({
		    mpn: z.string(),
		    packages: z.array(exports.PackageCatalogueSummarySchema),
		});
		exports.PackageCatalogueLookupResponseSchema = z.object({
		    schemaVersion: z.number().int(),
		    deploymentId: z.string().min(1),
		    results: z.array(exports.PackageCatalogueLookupResultSchema),
		});
		
	} (packageCatalogueLookup));
	return packageCatalogueLookup;
}

var hasRequiredParentDeployment;

function requireParentDeployment () {
	if (hasRequiredParentDeployment) return ParentDeployment;
	hasRequiredParentDeployment = 1;
	(function (exports) {
		var __createBinding = (ParentDeployment && ParentDeployment.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ParentDeployment && ParentDeployment.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePackageCatalogueLookup(), exports);
		
	} (ParentDeployment));
	return ParentDeployment;
}

var PackageDataAdjudication = {};

var packageDataAdjudication_types = {};

var jsonValue = {};

var hasRequiredJsonValue;

function requireJsonValue () {
	if (hasRequiredJsonValue) return jsonValue;
	hasRequiredJsonValue = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.JsonValueSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.JsonValueSchema = zod_1.z.lazy(() => zod_1.z.union([
		    zod_1.z.string(),
		    zod_1.z.number().finite(),
		    zod_1.z.boolean(),
		    zod_1.z.null(),
		    zod_1.z.array(exports.JsonValueSchema),
		    zod_1.z.record(zod_1.z.string(), exports.JsonValueSchema),
		]));
		
	} (jsonValue));
	return jsonValue;
}

var hasRequiredPackageDataAdjudication_types;

function requirePackageDataAdjudication_types () {
	if (hasRequiredPackageDataAdjudication_types) return packageDataAdjudication_types;
	hasRequiredPackageDataAdjudication_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PackageDataErrorQuerySchema = exports.AnchoredDatumSchema = exports.ComparedValueSchema = exports.DataAnchorAddressSchema = exports.ExperimentAssessmentStatusSchema = exports.ExperimentAssessmentStatus = exports.PackageDataErrorStatementKindSchema = exports.PackageDataErrorStatementKind = exports.DiscrepancyDirectionSchema = exports.DiscrepancyDirection = exports.RowExistenceSchema = exports.RowExistence = exports.ClauseCitationsSchema = exports.ClauseCitationSchema = exports.GroundTruthStandingStatusSchema = exports.GroundTruthStandingStatus = void 0;
		exports.datumAgrees = datumAgrees;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const slice_key_1 = requireSliceKey();
		const json_value_1 = requireJsonValue();
		var GroundTruthStandingStatus;
		(function (GroundTruthStandingStatus) {
		    GroundTruthStandingStatus["VettedAndCurrent"] = "vetted_and_current";
		    GroundTruthStandingStatus["VettedButResolutionChanged"] = "vetted_but_resolution_changed";
		    GroundTruthStandingStatus["RowRuledAbsent"] = "row_ruled_absent";
		    GroundTruthStandingStatus["NeverAdjudicated"] = "never_adjudicated";
		})(GroundTruthStandingStatus || (exports.GroundTruthStandingStatus = GroundTruthStandingStatus = {}));
		exports.GroundTruthStandingStatusSchema = z.nativeEnum(GroundTruthStandingStatus);
		exports.ClauseCitationSchema = z.object({
		    clauseId: z.string(),
		    rank: z.number().int().min(1),
		});
		exports.ClauseCitationsSchema = exports.ClauseCitationSchema.array().superRefine((citations, ctx) => {
		    const seenRanks = new Set();
		    const seenClauses = new Set();
		    for (const [index, citation] of citations.entries()) {
		        if (seenRanks.has(citation.rank)) {
		            ctx.addIssue({
		                code: z.ZodIssueCode.custom,
		                message: `Duplicate clause rank: ${citation.rank}`,
		                path: [index, 'rank'],
		            });
		        }
		        else {
		            seenRanks.add(citation.rank);
		        }
		        if (seenClauses.has(citation.clauseId)) {
		            ctx.addIssue({
		                code: z.ZodIssueCode.custom,
		                message: `Duplicate clause citation: ${citation.clauseId}`,
		                path: [index, 'clauseId'],
		            });
		        }
		        else {
		            seenClauses.add(citation.clauseId);
		        }
		    }
		});
		var RowExistence;
		(function (RowExistence) {
		    RowExistence["Present"] = "present";
		    RowExistence["Absent"] = "absent";
		})(RowExistence || (exports.RowExistence = RowExistence = {}));
		exports.RowExistenceSchema = z.nativeEnum(RowExistence);
		var DiscrepancyDirection;
		(function (DiscrepancyDirection) {
		    DiscrepancyDirection["Missing"] = "missing";
		    DiscrepancyDirection["Extra"] = "extra";
		})(DiscrepancyDirection || (exports.DiscrepancyDirection = DiscrepancyDirection = {}));
		exports.DiscrepancyDirectionSchema = z.nativeEnum(DiscrepancyDirection);
		var PackageDataErrorStatementKind;
		(function (PackageDataErrorStatementKind) {
		    PackageDataErrorStatementKind["Error"] = "error";
		    PackageDataErrorStatementKind["ErrorRetracted"] = "error_retracted";
		})(PackageDataErrorStatementKind || (exports.PackageDataErrorStatementKind = PackageDataErrorStatementKind = {}));
		exports.PackageDataErrorStatementKindSchema = z.nativeEnum(PackageDataErrorStatementKind);
		var ExperimentAssessmentStatus;
		(function (ExperimentAssessmentStatus) {
		    ExperimentAssessmentStatus["NotAssessed"] = "not_assessed";
		    ExperimentAssessmentStatus["Pending"] = "pending";
		    ExperimentAssessmentStatus["Complete"] = "complete";
		})(ExperimentAssessmentStatus || (exports.ExperimentAssessmentStatus = ExperimentAssessmentStatus = {}));
		exports.ExperimentAssessmentStatusSchema = z.nativeEnum(ExperimentAssessmentStatus);
		exports.DataAnchorAddressSchema = z.object({
		    packageId: z.string(),
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int(),
		    anchor: consensus_types_1.AnchorSchema,
		});
		exports.ComparedValueSchema = z.object({
		    raw: json_value_1.JsonValueSchema,
		    normalized: z.string().nullable(),
		});
		exports.AnchoredDatumSchema = z.discriminatedUnion('kind', [
		    z.object({ kind: z.literal(consensus_types_1.AnchorKind.Field), value: exports.ComparedValueSchema }),
		    z.object({ kind: z.literal(consensus_types_1.AnchorKind.Row), existence: exports.RowExistenceSchema, rowContentRaw: json_value_1.JsonValueSchema }),
		]);
		function datumAgrees(left, right) {
		    if (right === null || left.kind !== right.kind) {
		        return false;
		    }
		    if (left.kind === consensus_types_1.AnchorKind.Row) {
		        return right.kind === consensus_types_1.AnchorKind.Row && left.existence === right.existence;
		    }
		    return right.kind === consensus_types_1.AnchorKind.Field && left.value.normalized === right.value.normalized;
		}
		const PackageDataErrorQueryShape = z.object({
		    address: exports.DataAnchorAddressSchema,
		    datum: exports.AnchoredDatumSchema,
		});
		exports.PackageDataErrorQuerySchema = PackageDataErrorQueryShape.superRefine((query, ctx) => {
		    if (query.datum.kind === query.address.anchor.kind) {
		        return;
		    }
		    ctx.addIssue({
		        code: z.ZodIssueCode.custom,
		        message: query.address.anchor.kind === consensus_types_1.AnchorKind.Field ?
		            'A field anchor asks about a value and cannot carry a row datum.'
		            : 'A row anchor asks about the row itself and cannot carry a field datum.',
		        path: ['datum'],
		    });
		});
		
	} (packageDataAdjudication_types));
	return packageDataAdjudication_types;
}

var packageDataIdentity = {};

var hasRequiredPackageDataIdentity;

function requirePackageDataIdentity () {
	if (hasRequiredPackageDataIdentity) return packageDataIdentity;
	hasRequiredPackageDataIdentity = 1;
	Object.defineProperty(packageDataIdentity, "__esModule", { value: true });
	packageDataIdentity.dataAnchorAddressKey = dataAnchorAddressKey;
	packageDataIdentity.adjudicationSubjectComponent = adjudicationSubjectComponent;
	packageDataIdentity.computeErrorFingerprint = computeErrorFingerprint;
	packageDataIdentity.computeAdjudicationItemKey = computeAdjudicationItemKey;
	const consensus_types_1 = requireConsensus_types();
	const COMPONENT_SEPARATOR = '\x1F';
	const NULL_COMPONENT = '\x00';
	const NO_PROPOSAL_COMPONENT = '\x02';
	function dataAnchorAddressKey(address) {
	    return [
	        address.packageId,
	        address.sliceKey,
	        String(address.sliceVersion),
	        address.anchor.kind,
	        address.anchor.rowKey,
	        address.anchor.kind === consensus_types_1.AnchorKind.Field ? address.anchor.column : NULL_COMPONENT,
	    ].join(COMPONENT_SEPARATOR);
	}
	function adjudicationSubjectComponent(datum) {
	    if (datum === null) {
	        return NO_PROPOSAL_COMPONENT;
	    }
	    if (datum.kind === consensus_types_1.AnchorKind.Row) {
	        return datum.existence;
	    }
	    return datum.value.normalized ?? NULL_COMPONENT;
	}
	async function computeErrorFingerprint(address, datum) {
	    const subject = datum.kind === consensus_types_1.AnchorKind.Row ? NULL_COMPONENT : (datum.value.normalized ?? NULL_COMPONENT);
	    return sha256Hex([dataAnchorAddressKey(address), subject].join(COMPONENT_SEPARATOR));
	}
	async function computeAdjudicationItemKey(address, proposedValue) {
	    return sha256Hex([dataAnchorAddressKey(address), adjudicationSubjectComponent(proposedValue)].join(COMPONENT_SEPARATOR));
	}
	async function sha256Hex(input) {
	    const bytes = new TextEncoder().encode(input);
	    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
	    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
	}
	
	return packageDataIdentity;
}

var adjudicationRequest_types = {};

var serviceContracts = {};

var hasRequiredServiceContracts;

function requireServiceContracts () {
	if (hasRequiredServiceContracts) return serviceContracts;
	hasRequiredServiceContracts = 1;
	Object.defineProperty(serviceContracts, "__esModule", { value: true });
	serviceContracts.StatementProvenanceSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	serviceContracts.StatementProvenanceSchema = z.object({
	    statementId: z.string(),
	    adjudicatedByUserId: z.string(),
	    adjudicatedAt: z.date(),
	    comment: z.string().nullable(),
	    planId: z.string().nullable(),
	});
	
	return serviceContracts;
}

var hasRequiredAdjudicationRequest_types;

function requireAdjudicationRequest_types () {
	if (hasRequiredAdjudicationRequest_types) return adjudicationRequest_types;
	hasRequiredAdjudicationRequest_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AdjudicationSubmissionReceiptSchema = exports.AdjudicationItemResultSchema = exports.AdjudicationRequestSchema = exports.AdjudicationItemAnswerSchema = exports.KnownErrorCitationSchema = exports.AdjudicationAnsweredReasonSchema = exports.AdjudicationAnsweredReason = exports.AdjudicationItemDispositionSchema = exports.AdjudicationItemDisposition = exports.AdjudicationItemInputSchema = exports.ADJUDICATION_CONCERN_MAX_LENGTH = exports.AdjudicationSourceKindSchema = exports.AdjudicationSourceKind = void 0;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const property_key_1 = requirePropertyKey();
		const slice_key_1 = requireSliceKey();
		const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
		const service_contracts_1 = requireServiceContracts();
		var AdjudicationSourceKind;
		(function (AdjudicationSourceKind) {
		    AdjudicationSourceKind["Experiment"] = "experiment";
		    AdjudicationSourceKind["AgentAudit"] = "agent_audit";
		    AdjudicationSourceKind["Manual"] = "manual";
		})(AdjudicationSourceKind || (exports.AdjudicationSourceKind = AdjudicationSourceKind = {}));
		exports.AdjudicationSourceKindSchema = z.nativeEnum(AdjudicationSourceKind);
		exports.ADJUDICATION_CONCERN_MAX_LENGTH = 1000;
		const AdjudicationItemInputShape = z.object({
		    address: package_data_adjudication_types_1.DataAnchorAddressSchema,
		    proposedValue: package_data_adjudication_types_1.AnchoredDatumSchema.nullable(),
		    concern: z.string().trim().min(1).max(exports.ADJUDICATION_CONCERN_MAX_LENGTH).nullable().optional(),
		});
		exports.AdjudicationItemInputSchema = AdjudicationItemInputShape.superRefine((item, ctx) => {
		    if (item.proposedValue === null || item.proposedValue.kind === item.address.anchor.kind) {
		        return;
		    }
		    ctx.addIssue({
		        code: z.ZodIssueCode.custom,
		        message: item.address.anchor.kind === consensus_types_1.AnchorKind.Field ?
		            'A field anchor puts a value in doubt and cannot carry a row datum.'
		            : 'A row anchor puts the row itself in doubt and cannot carry a field datum.',
		        path: ['proposedValue'],
		    });
		});
		var AdjudicationItemDisposition;
		(function (AdjudicationItemDisposition) {
		    AdjudicationItemDisposition["Answered"] = "answered";
		    AdjudicationItemDisposition["PendingExisting"] = "pending_existing";
		    AdjudicationItemDisposition["PendingNew"] = "pending_new";
		})(AdjudicationItemDisposition || (exports.AdjudicationItemDisposition = AdjudicationItemDisposition = {}));
		exports.AdjudicationItemDispositionSchema = z.nativeEnum(AdjudicationItemDisposition);
		var AdjudicationAnsweredReason;
		(function (AdjudicationAnsweredReason) {
		    AdjudicationAnsweredReason["GroundTruthVetted"] = "ground_truth_vetted";
		    AdjudicationAnsweredReason["ProposedValueCorrect"] = "proposed_value_correct";
		    AdjudicationAnsweredReason["ProposedValueKnownError"] = "proposed_value_known_error";
		    AdjudicationAnsweredReason["AddressRuledAbsent"] = "address_ruled_absent";
		})(AdjudicationAnsweredReason || (exports.AdjudicationAnsweredReason = AdjudicationAnsweredReason = {}));
		exports.AdjudicationAnsweredReasonSchema = z.nativeEnum(AdjudicationAnsweredReason);
		exports.KnownErrorCitationSchema = z.object({
		    statementId: z.string(),
		    violatedClauses: package_data_adjudication_types_1.ClauseCitationSchema.array(),
		    errorCodeIds: z.string().array(),
		    comment: z.string().nullable(),
		    adjudicatedByUserId: z.string(),
		    adjudicatedByUserName: z.string().nullable(),
		    adjudicatedAt: z.date(),
		    planId: z.string().nullable(),
		});
		exports.AdjudicationItemAnswerSchema = z.object({
		    reason: exports.AdjudicationAnsweredReasonSchema,
		    groundTruthStatus: package_data_adjudication_types_1.GroundTruthStandingStatusSchema,
		    groundTruth: package_data_adjudication_types_1.AnchoredDatumSchema.nullable(),
		    groundTruthClauses: package_data_adjudication_types_1.ClauseCitationSchema.array(),
		    groundTruthProvenance: service_contracts_1.StatementProvenanceSchema.nullable(),
		    errorCitation: exports.KnownErrorCitationSchema.nullable(),
		});
		exports.AdjudicationRequestSchema = z.object({
		    id: z.string(),
		    planId: z.string().nullable(),
		    packageId: z.string(),
		    propertyKey: property_key_1.PropertyKeySchema,
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int(),
		    createdAt: z.date(),
		});
		exports.AdjudicationItemResultSchema = z.object({
		    address: package_data_adjudication_types_1.DataAnchorAddressSchema,
		    disposition: exports.AdjudicationItemDispositionSchema,
		    answer: exports.AdjudicationItemAnswerSchema.nullable(),
		    requestId: z.string().nullable(),
		    planId: z.string().nullable(),
		});
		exports.AdjudicationSubmissionReceiptSchema = z.object({
		    items: exports.AdjudicationItemResultSchema.array(),
		    newRequests: exports.AdjudicationRequestSchema.array(),
		    existingRequests: exports.AdjudicationRequestSchema.array(),
		});
		
	} (adjudicationRequest_types));
	return adjudicationRequest_types;
}

var experimentErrorProfile_types = {};

var packageDataSpecStatus = {};

var hasRequiredPackageDataSpecStatus;

function requirePackageDataSpecStatus () {
	if (hasRequiredPackageDataSpecStatus) return packageDataSpecStatus;
	hasRequiredPackageDataSpecStatus = 1;
	Object.defineProperty(packageDataSpecStatus, "__esModule", { value: true });
	packageDataSpecStatus.PackageDataSpecStatusSchema = packageDataSpecStatus.PackageDataSpecStatus = void 0;
	const z = /*@__PURE__*/ requireZod();
	var PackageDataSpecStatus;
	(function (PackageDataSpecStatus) {
	    PackageDataSpecStatus["Active"] = "active";
	    PackageDataSpecStatus["Retired"] = "retired";
	})(PackageDataSpecStatus || (packageDataSpecStatus.PackageDataSpecStatus = PackageDataSpecStatus = {}));
	packageDataSpecStatus.PackageDataSpecStatusSchema = z.nativeEnum(PackageDataSpecStatus);
	
	return packageDataSpecStatus;
}

var planStatus = {};

var hasRequiredPlanStatus;

function requirePlanStatus () {
	if (hasRequiredPlanStatus) return planStatus;
	hasRequiredPlanStatus = 1;
	Object.defineProperty(planStatus, "__esModule", { value: true });
	planStatus.PlanStatus = void 0;
	planStatus.canStillAnswer = canStillAnswer;
	var PlanStatus;
	(function (PlanStatus) {
	    PlanStatus["Pending"] = "pending";
	    PlanStatus["Complete"] = "complete";
	    PlanStatus["Failed"] = "failed";
	    PlanStatus["Cancelled"] = "cancelled";
	})(PlanStatus || (planStatus.PlanStatus = PlanStatus = {}));
	function canStillAnswer(status) {
	    return status === PlanStatus.Pending;
	}
	
	return planStatus;
}

var hasRequiredExperimentErrorProfile_types;

function requireExperimentErrorProfile_types () {
	if (hasRequiredExperimentErrorProfile_types) return experimentErrorProfile_types;
	hasRequiredExperimentErrorProfile_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ExperimentErrorProfileSchema = exports.ProfileDispatchSchema = exports.ProfileSampleSchema = exports.ProfileSampleRunSchema = exports.ProfileErrorCodeBucketSchema = exports.ProfileClauseBucketSchema = exports.ProfileOccurrenceSchema = exports.ProfileCitedClauseSchema = exports.ProfileDictionarySchema = exports.ProfileErrorCodeEntrySchema = exports.ProfileClauseEntrySchema = exports.ProfileCurrentClauseSchema = exports.ProfileStatusSchema = exports.ProfileTotalsSchema = exports.ProfileExperimentRefSchema = exports.ProfiledDisagreementSchema = exports.DisagreementClassificationSchema = exports.SampleRefSchema = exports.DisagreementResolutionSchema = exports.DisagreementResolution = exports.EXPERIMENT_ERROR_PROFILE_VERSION = void 0;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const property_key_1 = requirePropertyKey();
		const slice_key_1 = requireSliceKey();
		const package_data_spec_status_1 = requirePackageDataSpecStatus();
		const plan_status_1 = requirePlanStatus();
		const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
		exports.EXPERIMENT_ERROR_PROFILE_VERSION = 4;
		var DisagreementResolution;
		(function (DisagreementResolution) {
		    DisagreementResolution["Error"] = "error";
		    DisagreementResolution["NotAnError"] = "not_an_error";
		    DisagreementResolution["AwaitingExpert"] = "awaiting_expert";
		    DisagreementResolution["NotDispatched"] = "not_dispatched";
		    DisagreementResolution["Structural"] = "structural";
		})(DisagreementResolution || (exports.DisagreementResolution = DisagreementResolution = {}));
		exports.DisagreementResolutionSchema = z.nativeEnum(DisagreementResolution);
		exports.SampleRefSchema = z.object({
		    sampleResultId: z.string(),
		    packageId: z.string(),
		    runNumber: z.number().int(),
		    mpn: z.string(),
		    packageUrl: z.string(),
		});
		exports.DisagreementClassificationSchema = z.object({
		    clauses: package_data_adjudication_types_1.ClauseCitationSchema.array(),
		    errorCodeIds: z.string().array(),
		    comment: z.string().nullable(),
		    statementId: z.string(),
		    adjudicatedByUserId: z.string(),
		    adjudicatedByUserName: z.string().nullable(),
		    adjudicatedAt: z.coerce.date(),
		    planId: z.string().nullable(),
		});
		const ComparedSideSchema = z.union([package_data_adjudication_types_1.ComparedValueSchema, package_data_adjudication_types_1.RowExistenceSchema]);
		exports.ProfiledDisagreementSchema = z.object({
		    sampleResultId: z.string(),
		    packageId: z.string(),
		    runNumber: z.number().int(),
		    anchor: consensus_types_1.AnchorSchema,
		    direction: package_data_adjudication_types_1.DiscrepancyDirectionSchema.nullable(),
		    scoredGroundTruth: ComparedSideSchema,
		    vettedGroundTruth: package_data_adjudication_types_1.AnchoredDatumSchema.nullable(),
		    inferred: ComparedSideSchema,
		    resolution: exports.DisagreementResolutionSchema,
		    groundTruthChanged: z.boolean(),
		    classification: exports.DisagreementClassificationSchema.nullable(),
		    requestId: z.string().nullable(),
		    planId: z.string().nullable(),
		});
		exports.ProfileExperimentRefSchema = z.object({
		    id: z.string(),
		    task: z.string(),
		    description: z.string(),
		    dataset: z.object({ id: z.string(), name: z.string(), version: z.number().int() }),
		    scoringId: z.string().nullable(),
		});
		exports.ProfileTotalsSchema = z.object({
		    disagreements: z.number().int(),
		    errors: z.number().int(),
		    notErrors: z.number().int(),
		    awaitingExpert: z.number().int(),
		    notDispatched: z.number().int(),
		    structural: z.number().int(),
		});
		exports.ProfileStatusSchema = z.object({
		    assessmentStatus: package_data_adjudication_types_1.ExperimentAssessmentStatusSchema,
		    totals: exports.ProfileTotalsSchema,
		    resolvedShare: z.number(),
		    firstDispatchedAt: z.coerce.date().nullable(),
		    lastAssessedAt: z.coerce.date(),
		    openPlanIds: z.string().array(),
		    notAssessableSampleResultIds: z.string().array(),
		});
		exports.ProfileCurrentClauseSchema = z.object({
		    clauseId: z.string(),
		    version: z.number().int(),
		    description: z.string(),
		});
		exports.ProfileClauseEntrySchema = z.object({
		    clauseId: z.string(),
		    propertyKey: property_key_1.PropertyKeySchema,
		    clauseNumber: z.number().int(),
		    version: z.number().int(),
		    shortName: z.string(),
		    description: z.string(),
		    status: package_data_spec_status_1.PackageDataSpecStatusSchema,
		    current: exports.ProfileCurrentClauseSchema.nullable(),
		    replacedByClauseId: z.string().nullable(),
		});
		exports.ProfileErrorCodeEntrySchema = z.object({
		    errorCodeId: z.string(),
		    propertyKey: property_key_1.PropertyKeySchema,
		    code: z.string(),
		    description: z.string(),
		    status: package_data_spec_status_1.PackageDataSpecStatusSchema,
		});
		exports.ProfileDictionarySchema = z.object({
		    clauses: exports.ProfileClauseEntrySchema.array(),
		    errorCodes: exports.ProfileErrorCodeEntrySchema.array(),
		});
		exports.ProfileCitedClauseSchema = z.object({
		    clauseId: z.string(),
		    propertyKey: property_key_1.PropertyKeySchema,
		    clauseNumber: z.number().int(),
		    rank: z.number().int().min(1),
		});
		exports.ProfileOccurrenceSchema = exports.SampleRefSchema.extend({
		    anchor: consensus_types_1.AnchorSchema,
		    direction: package_data_adjudication_types_1.DiscrepancyDirectionSchema.nullable(),
		    scoredGroundTruth: ComparedSideSchema,
		    vettedGroundTruth: package_data_adjudication_types_1.AnchoredDatumSchema.nullable(),
		    inferred: ComparedSideSchema,
		    comment: z.string().nullable(),
		    citedClauses: exports.ProfileCitedClauseSchema.array(),
		});
		exports.ProfileClauseBucketSchema = z.object({
		    propertyKey: property_key_1.PropertyKeySchema,
		    clauseNumber: z.number().int(),
		    citedClauseIds: z.string().array(),
		    occurrenceCount: z.number().int(),
		    sampleCount: z.number().int(),
		    distinctIssueCount: z.number().int(),
		    occurrenceShare: z.number(),
		    occurrences: exports.ProfileOccurrenceSchema.array(),
		});
		exports.ProfileErrorCodeBucketSchema = z.object({
		    errorCodeId: z.string(),
		    occurrenceCount: z.number().int(),
		    sampleCount: z.number().int(),
		    distinctIssueCount: z.number().int(),
		    occurrenceShare: z.number(),
		    occurrences: exports.ProfileOccurrenceSchema.array(),
		});
		exports.ProfileSampleRunSchema = z.object({
		    runNumber: z.number().int(),
		    sampleResultId: z.string(),
		    disagreements: exports.ProfiledDisagreementSchema.array(),
		});
		exports.ProfileSampleSchema = z.object({
		    packageId: z.string(),
		    mpn: z.string(),
		    packageUrl: z.string(),
		    runs: exports.ProfileSampleRunSchema.array(),
		});
		exports.ProfileDispatchSchema = z.object({
		    requestId: z.string(),
		    planId: z.string().nullable(),
		    packageId: z.string(),
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int(),
		    planStatus: z.nativeEnum(plan_status_1.PlanStatus).nullable(),
		    itemCount: z.number().int(),
		    createdAt: z.coerce.date(),
		});
		exports.ExperimentErrorProfileSchema = z.object({
		    profileVersion: z.literal(exports.EXPERIMENT_ERROR_PROFILE_VERSION),
		    generatedAt: z.coerce.date(),
		    experiment: exports.ProfileExperimentRefSchema,
		    slice: z.object({ sliceKey: slice_key_1.SliceKeySchema, sliceVersion: z.number().int() }),
		    status: exports.ProfileStatusSchema,
		    dictionary: exports.ProfileDictionarySchema,
		    byClause: exports.ProfileClauseBucketSchema.array(),
		    byErrorCode: exports.ProfileErrorCodeBucketSchema.array(),
		    samples: exports.ProfileSampleSchema.array(),
		    unresolved: exports.ProfiledDisagreementSchema.array(),
		    groundTruthCorrections: exports.ProfiledDisagreementSchema.array(),
		    dispatches: exports.ProfileDispatchSchema.array(),
		});
		
	} (experimentErrorProfile_types));
	return experimentErrorProfile_types;
}

var packageDataAudit_types = {};

var hasRequiredPackageDataAudit_types;

function requirePackageDataAudit_types () {
	if (hasRequiredPackageDataAudit_types) return packageDataAudit_types;
	hasRequiredPackageDataAudit_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PackageDataAuditResponseSchema = exports.PackageDataAuditUnresolvableResponseSchema = exports.PackageDataAuditHintSchema = exports.PackageDataAuditAnsweredResponseSchema = exports.PackageDataAuditItemResponseSchema = exports.PackageDataAuditOpinionItemSchema = exports.PackageDataAuditAdjudicatedItemSchema = exports.PackageDataAuditMetadataSchema = exports.ProposedValueAssessmentSchema = exports.PackageDataAuditRequestSchema = exports.PackageDataAuditRequestShape = exports.PackageDataAuditItemShape = exports.ProposedValueVerdictSchema = exports.ProposedValueVerdict = exports.PackageDataAuditItemStatusSchema = exports.PackageDataAuditItemStatus = exports.PackageDataAuditStatusSchema = exports.PackageDataAuditStatus = exports.PackageDataAuditQuestionSchema = exports.PackageDataAuditQuestion = exports.PACKAGE_DATA_AUDIT_MAX_ITEMS = void 0;
		exports.refinePackageDataAuditRequest = refinePackageDataAuditRequest;
		exports.toPackageDataAuditItemResponse = toPackageDataAuditItemResponse;
		exports.toPackageDataAuditResponse = toPackageDataAuditResponse;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const property_key_1 = requirePropertyKey();
		const slice_key_1 = requireSliceKey();
		const adjudication_request_types_1 = requireAdjudicationRequest_types();
		exports.PACKAGE_DATA_AUDIT_MAX_ITEMS = 50;
		var PackageDataAuditQuestion;
		(function (PackageDataAuditQuestion) {
		    PackageDataAuditQuestion["Value"] = "value";
		    PackageDataAuditQuestion["Existence"] = "existence";
		})(PackageDataAuditQuestion || (exports.PackageDataAuditQuestion = PackageDataAuditQuestion = {}));
		exports.PackageDataAuditQuestionSchema = z.nativeEnum(PackageDataAuditQuestion);
		var PackageDataAuditStatus;
		(function (PackageDataAuditStatus) {
		    PackageDataAuditStatus["Answered"] = "answered";
		    PackageDataAuditStatus["RequestUnresolvable"] = "request_unresolvable";
		})(PackageDataAuditStatus || (exports.PackageDataAuditStatus = PackageDataAuditStatus = {}));
		exports.PackageDataAuditStatusSchema = z.nativeEnum(PackageDataAuditStatus);
		var PackageDataAuditItemStatus;
		(function (PackageDataAuditItemStatus) {
		    PackageDataAuditItemStatus["Adjudicated"] = "adjudicated";
		    PackageDataAuditItemStatus["AdjudicationPending"] = "adjudication_pending";
		    PackageDataAuditItemStatus["NotAdjudicated"] = "not_adjudicated";
		})(PackageDataAuditItemStatus || (exports.PackageDataAuditItemStatus = PackageDataAuditItemStatus = {}));
		exports.PackageDataAuditItemStatusSchema = z.nativeEnum(PackageDataAuditItemStatus);
		var ProposedValueVerdict;
		(function (ProposedValueVerdict) {
		    ProposedValueVerdict["Incorrect"] = "incorrect";
		})(ProposedValueVerdict || (exports.ProposedValueVerdict = ProposedValueVerdict = {}));
		exports.ProposedValueVerdictSchema = z.nativeEnum(ProposedValueVerdict);
		exports.PackageDataAuditItemShape = z.object({
		    row: z
		        .record(z.string(), z.string())
		        .describe('Facts identifying the row, keyed by column name, such as { "signalName": "NRST" } or { "pinId": "A3" }. ' +
		        "The property's identity columns locate the row when given. Without them, the value columns you " +
		        'give locate it instead and must match exactly one row. Other keys are ignored.'),
		    question: exports.PackageDataAuditQuestionSchema.describe('"value" asks whether one cell is right; "existence" asks whether the row should be in the table at all.'),
		    column: z
		        .string()
		        .min(1)
		        .optional()
		        .describe('The column in doubt. Required for a value question; omit for an existence question.'),
		    observedValue: z
		        .string()
		        .optional()
		        .describe('The value you see at that cell. Send it whenever you have it: a mismatch means the row resolved ' +
		        'wrongly or your read is stale.'),
		    proposedValue: z
		        .string()
		        .optional()
		        .describe('What you think the cell should hold, only when you have a specific alternative in mind. ' +
		        'For an existence question, "absent" or "present".'),
		    concern: z
		        .string()
		        .trim()
		        .min(1)
		        .max(adjudication_request_types_1.ADJUDICATION_CONCERN_MAX_LENGTH)
		        .optional()
		        .describe('Why you believe the value is wrong. The expert who reviews the cell reads this when your call raises ' +
		        `the review. At most ${adjudication_request_types_1.ADJUDICATION_CONCERN_MAX_LENGTH} characters.`),
		});
		exports.PackageDataAuditRequestShape = z.object({
		    packageId: z.string().min(1).describe('The package whose data is in doubt.'),
		    propertyKey: property_key_1.PropertyKeySchema.describe('The package-data property whose table the items belong to.'),
		    items: z
		        .array(exports.PackageDataAuditItemShape)
		        .min(1)
		        .max(exports.PACKAGE_DATA_AUDIT_MAX_ITEMS)
		        .describe('Every cell or row of this table you doubt, one item each. All items are placed together or the ' +
		        `request is refused as a whole; at most ${exports.PACKAGE_DATA_AUDIT_MAX_ITEMS} per call.`),
		});
		function refinePackageDataAuditRequest(request, ctx) {
		    request.items.forEach((item, index) => {
		        if (item.question === PackageDataAuditQuestion.Value && item.column === undefined) {
		            ctx.addIssue({
		                code: z.ZodIssueCode.custom,
		                message: `Item ${index}: a value question names the column in doubt.`,
		                path: ['items', index, 'column'],
		            });
		        }
		        if (item.question === PackageDataAuditQuestion.Existence && item.column !== undefined) {
		            ctx.addIssue({
		                code: z.ZodIssueCode.custom,
		                message: `Item ${index}: an existence question is about the whole row and takes no column.`,
		                path: ['items', index, 'column'],
		            });
		        }
		    });
		}
		exports.PackageDataAuditRequestSchema = exports.PackageDataAuditRequestShape.superRefine(refinePackageDataAuditRequest);
		exports.ProposedValueAssessmentSchema = z.object({
		    verdict: exports.ProposedValueVerdictSchema,
		    reason: z.string(),
		});
		exports.PackageDataAuditMetadataSchema = z.object({
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int(),
		    anchor: consensus_types_1.AnchorSchema,
		    requestId: z.string().optional(),
		    planId: z.string().optional(),
		    citedClauseIds: z.string().array(),
		});
		exports.PackageDataAuditAdjudicatedItemSchema = z.object({
		    status: z.literal(PackageDataAuditItemStatus.Adjudicated),
		    subject: z.string(),
		    value: z.string(),
		    explanation: z.string(),
		    proposedValueAssessment: exports.ProposedValueAssessmentSchema.optional(),
		});
		exports.PackageDataAuditOpinionItemSchema = z.object({
		    status: z.union([
		        z.literal(PackageDataAuditItemStatus.AdjudicationPending),
		        z.literal(PackageDataAuditItemStatus.NotAdjudicated),
		    ]),
		    subject: z.string(),
		    value: z.string().optional(),
		    explanation: z.string().optional(),
		});
		exports.PackageDataAuditItemResponseSchema = z.union([
		    exports.PackageDataAuditAdjudicatedItemSchema,
		    exports.PackageDataAuditOpinionItemSchema,
		]);
		exports.PackageDataAuditAnsweredResponseSchema = z.object({
		    status: z.literal(PackageDataAuditStatus.Answered),
		    items: exports.PackageDataAuditItemResponseSchema.array(),
		});
		exports.PackageDataAuditHintSchema = z.object({
		    index: z.number().int().nonnegative().optional(),
		    hint: z.string(),
		});
		exports.PackageDataAuditUnresolvableResponseSchema = z.object({
		    status: z.literal(PackageDataAuditStatus.RequestUnresolvable),
		    hints: exports.PackageDataAuditHintSchema.array().min(1),
		});
		exports.PackageDataAuditResponseSchema = z.union([
		    exports.PackageDataAuditAnsweredResponseSchema,
		    exports.PackageDataAuditUnresolvableResponseSchema,
		]);
		function toPackageDataAuditItemResponse(item) {
		    switch (item.status) {
		        case PackageDataAuditItemStatus.Adjudicated:
		            return {
		                status: item.status,
		                subject: item.subject,
		                value: item.value,
		                explanation: item.explanation,
		                ...(item.proposedValueAssessment === undefined ?
		                    {}
		                    : { proposedValueAssessment: item.proposedValueAssessment }),
		            };
		        case PackageDataAuditItemStatus.AdjudicationPending:
		        case PackageDataAuditItemStatus.NotAdjudicated:
		            return {
		                status: item.status,
		                subject: item.subject,
		                ...(item.value === undefined ? {} : { value: item.value }),
		                ...(item.explanation === undefined ? {} : { explanation: item.explanation }),
		            };
		    }
		}
		function toPackageDataAuditResponse(result) {
		    if (result.status === PackageDataAuditStatus.RequestUnresolvable) {
		        return {
		            status: result.status,
		            hints: result.hints.map((entry) => entry.index === undefined ? { hint: entry.hint } : { index: entry.index, hint: entry.hint }),
		        };
		    }
		    return { status: result.status, items: result.items.map(toPackageDataAuditItemResponse) };
		}
		
	} (packageDataAudit_types));
	return packageDataAudit_types;
}

var hasRequiredPackageDataAdjudication;

function requirePackageDataAdjudication () {
	if (hasRequiredPackageDataAdjudication) return PackageDataAdjudication;
	hasRequiredPackageDataAdjudication = 1;
	(function (exports) {
		var __createBinding = (PackageDataAdjudication && PackageDataAdjudication.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (PackageDataAdjudication && PackageDataAdjudication.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePackageDataAdjudication_types(), exports);
		__exportStar(requirePackageDataIdentity(), exports);
		__exportStar(requireAdjudicationRequest_types(), exports);
		__exportStar(requireServiceContracts(), exports);
		__exportStar(requireExperimentErrorProfile_types(), exports);
		__exportStar(requirePackageDataAudit_types(), exports);
		
	} (PackageDataAdjudication));
	return PackageDataAdjudication;
}

var PackageDataSpec = {};

var specHandle = {};

var hasRequiredSpecHandle;

function requireSpecHandle () {
	if (hasRequiredSpecHandle) return specHandle;
	hasRequiredSpecHandle = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SpecHandleSchema = exports.SPEC_HANDLE_PATTERN = exports.SPEC_HANDLE_MAX_LENGTH = void 0;
		exports.normalizeSpecHandle = normalizeSpecHandle;
		const z = /*@__PURE__*/ requireZod();
		exports.SPEC_HANDLE_MAX_LENGTH = 64;
		exports.SPEC_HANDLE_PATTERN = /^[a-z][a-z0-9_]*$/;
		function normalizeSpecHandle(raw) {
		    return raw
		        .trim()
		        .toLowerCase()
		        .replace(/[^a-z0-9]+/g, '_')
		        .replace(/^_+|_+$/g, '');
		}
		exports.SpecHandleSchema = z
		    .string()
		    .transform(normalizeSpecHandle)
		    .refine((handle) => exports.SPEC_HANDLE_PATTERN.test(handle), {
		    message: 'A handle must start with a letter and contain only lowercase letters, digits, and underscores.',
		})
		    .refine((handle) => handle.length <= exports.SPEC_HANDLE_MAX_LENGTH, {
		    message: `A handle must be at most ${exports.SPEC_HANDLE_MAX_LENGTH} characters long.`,
		});
		
	} (specHandle));
	return specHandle;
}

var packageDataSpecClause = {};

var hasRequiredPackageDataSpecClause;

function requirePackageDataSpecClause () {
	if (hasRequiredPackageDataSpecClause) return packageDataSpecClause;
	hasRequiredPackageDataSpecClause = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ClauseCitationResolutionSchema = exports.PackageDataSpecClauseLineageSchema = exports.PackageDataSpecClauseSchema = exports.SpecAuthorSchema = void 0;
		exports.formatClauseCitation = formatClauseCitation;
		const z = /*@__PURE__*/ requireZod();
		const property_key_1 = requirePropertyKey();
		const package_data_spec_status_1 = requirePackageDataSpecStatus();
		exports.SpecAuthorSchema = z.object({
		    username: z.string(),
		    displayName: z.string().nullable(),
		});
		exports.PackageDataSpecClauseSchema = z.object({
		    id: z.string(),
		    propertyKey: property_key_1.PropertyKeySchema,
		    clauseNumber: z.number().int(),
		    version: z.number().int(),
		    shortName: z.string(),
		    description: z.string(),
		    status: package_data_spec_status_1.PackageDataSpecStatusSchema,
		    isCurrentVersion: z.boolean(),
		    nextVersionId: z.string().nullable(),
		    replacedByClauseId: z.string().nullable(),
		    retirementComment: z.string().nullable(),
		    createdByUserId: z.string().nullable(),
		    createdBy: exports.SpecAuthorSchema.nullable(),
		    createdAt: z.date(),
		    retiredByUserId: z.string().nullable(),
		    retiredBy: exports.SpecAuthorSchema.nullable(),
		    retiredAt: z.date().nullable(),
		});
		exports.PackageDataSpecClauseLineageSchema = z.object({
		    propertyKey: property_key_1.PropertyKeySchema,
		    clauseNumber: z.number().int(),
		    versions: z.array(exports.PackageDataSpecClauseSchema),
		    replacedBy: exports.PackageDataSpecClauseSchema.nullable(),
		});
		exports.ClauseCitationResolutionSchema = z.object({
		    cited: exports.PackageDataSpecClauseSchema,
		    currentVersion: exports.PackageDataSpecClauseSchema,
		    replacement: exports.PackageDataSpecClauseSchema.nullable(),
		});
		function formatClauseCitation(clause) {
		    return `${clause.propertyKey} #${clause.clauseNumber} v${clause.version}`;
		}
		
	} (packageDataSpecClause));
	return packageDataSpecClause;
}

var packageDataSpecClause_inputs = {};

var hasRequiredPackageDataSpecClause_inputs;

function requirePackageDataSpecClause_inputs () {
	if (hasRequiredPackageDataSpecClause_inputs) return packageDataSpecClause_inputs;
	hasRequiredPackageDataSpecClause_inputs = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ReinstateClauseInputSchema = exports.RetireClauseInputSchema = exports.RecordClauseReplacementInputSchema = exports.SupersedeClauseInputSchema = exports.ClauseReplacementSchema = exports.ExistingClauseReplacementSchema = exports.NewClauseReplacementSchema = exports.ClauseReplacementKindSchema = exports.ClauseReplacementKind = exports.ReviseClauseWordingInputSchema = exports.CreateClauseInputSchema = exports.ListCurrentClausesInputSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const property_key_1 = requirePropertyKey();
		const spec_handle_1 = requireSpecHandle();
		exports.ListCurrentClausesInputSchema = z.object({
		    propertyKeys: z
		        .array(property_key_1.PropertyKeySchema)
		        .optional()
		        .describe('Package-data properties to restrict the specification to. Omit to read every property.'),
		    includeRetired: z
		        .boolean()
		        .optional()
		        .describe('Include clauses that have been retired or superseded. Defaults to false.'),
		    includePriorVersions: z
		        .boolean()
		        .optional()
		        .describe('Include the superseded wordings of each clause alongside the one in force. Defaults to false.'),
		    search: z
		        .string()
		        .optional()
		        .describe('Free-text term matched case-insensitively against clause short names and wording.'),
		});
		exports.CreateClauseInputSchema = z.object({
		    propertyKey: property_key_1.PropertyKeySchema,
		    shortName: spec_handle_1.SpecHandleSchema,
		    description: z.string().min(1),
		});
		exports.ReviseClauseWordingInputSchema = z.object({
		    clauseId: z.string(),
		    shortName: spec_handle_1.SpecHandleSchema.optional(),
		    description: z.string().min(1).optional(),
		});
		var ClauseReplacementKind;
		(function (ClauseReplacementKind) {
		    ClauseReplacementKind["NewClause"] = "new_clause";
		    ClauseReplacementKind["ExistingClause"] = "existing_clause";
		})(ClauseReplacementKind || (exports.ClauseReplacementKind = ClauseReplacementKind = {}));
		exports.ClauseReplacementKindSchema = z.nativeEnum(ClauseReplacementKind);
		exports.NewClauseReplacementSchema = z.object({
		    kind: z.literal(ClauseReplacementKind.NewClause),
		    draft: exports.CreateClauseInputSchema,
		});
		exports.ExistingClauseReplacementSchema = z.object({
		    kind: z.literal(ClauseReplacementKind.ExistingClause),
		    clauseId: z.string(),
		});
		exports.ClauseReplacementSchema = z.discriminatedUnion('kind', [
		    exports.NewClauseReplacementSchema,
		    exports.ExistingClauseReplacementSchema,
		]);
		exports.SupersedeClauseInputSchema = z.object({
		    clauseId: z.string(),
		    replacement: exports.ClauseReplacementSchema,
		    retirementComment: z.string().optional(),
		});
		exports.RecordClauseReplacementInputSchema = z.object({
		    clauseId: z.string(),
		    replacementClauseId: z.string(),
		});
		exports.RetireClauseInputSchema = z.object({
		    clauseId: z.string(),
		    retirementComment: z.string().optional(),
		});
		exports.ReinstateClauseInputSchema = z.object({
		    clauseId: z.string(),
		});
		
	} (packageDataSpecClause_inputs));
	return packageDataSpecClause_inputs;
}

var packageInferenceErrorCode = {};

var hasRequiredPackageInferenceErrorCode;

function requirePackageInferenceErrorCode () {
	if (hasRequiredPackageInferenceErrorCode) return packageInferenceErrorCode;
	hasRequiredPackageInferenceErrorCode = 1;
	Object.defineProperty(packageInferenceErrorCode, "__esModule", { value: true });
	packageInferenceErrorCode.PackageInferenceErrorCodeSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const property_key_1 = requirePropertyKey();
	const package_data_spec_clause_1 = requirePackageDataSpecClause();
	const package_data_spec_status_1 = requirePackageDataSpecStatus();
	packageInferenceErrorCode.PackageInferenceErrorCodeSchema = z.object({
	    id: z.string(),
	    propertyKey: property_key_1.PropertyKeySchema,
	    code: z.string(),
	    description: z.string(),
	    status: package_data_spec_status_1.PackageDataSpecStatusSchema,
	    retirementComment: z.string().nullable(),
	    createdByUserId: z.string().nullable(),
	    createdBy: package_data_spec_clause_1.SpecAuthorSchema.nullable(),
	    createdAt: z.date(),
	    updatedAt: z.date(),
	    retiredByUserId: z.string().nullable(),
	    retiredBy: package_data_spec_clause_1.SpecAuthorSchema.nullable(),
	    retiredAt: z.date().nullable(),
	});
	
	return packageInferenceErrorCode;
}

var packageInferenceErrorCode_inputs = {};

var hasRequiredPackageInferenceErrorCode_inputs;

function requirePackageInferenceErrorCode_inputs () {
	if (hasRequiredPackageInferenceErrorCode_inputs) return packageInferenceErrorCode_inputs;
	hasRequiredPackageInferenceErrorCode_inputs = 1;
	Object.defineProperty(packageInferenceErrorCode_inputs, "__esModule", { value: true });
	packageInferenceErrorCode_inputs.ReinstateErrorCodeInputSchema = packageInferenceErrorCode_inputs.RetireErrorCodeInputSchema = packageInferenceErrorCode_inputs.UpdateErrorCodeInputSchema = packageInferenceErrorCode_inputs.CreateErrorCodeInputSchema = packageInferenceErrorCode_inputs.ListErrorCodesInputSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const property_key_1 = requirePropertyKey();
	const spec_handle_1 = requireSpecHandle();
	packageInferenceErrorCode_inputs.ListErrorCodesInputSchema = z.object({
	    propertyKeys: z
	        .array(property_key_1.PropertyKeySchema)
	        .optional()
	        .describe('Package-data properties to restrict the codes to. Omit to read every property.'),
	    includeRetired: z.boolean().optional().describe('Include codes that have been retired. Defaults to false.'),
	});
	packageInferenceErrorCode_inputs.CreateErrorCodeInputSchema = z.object({
	    propertyKey: property_key_1.PropertyKeySchema,
	    code: spec_handle_1.SpecHandleSchema,
	    description: z.string().min(1),
	});
	packageInferenceErrorCode_inputs.UpdateErrorCodeInputSchema = z.object({
	    errorCodeId: z.string(),
	    code: spec_handle_1.SpecHandleSchema.optional(),
	    description: z.string().min(1).optional(),
	});
	packageInferenceErrorCode_inputs.RetireErrorCodeInputSchema = z.object({
	    errorCodeId: z.string(),
	    retirementComment: z.string().optional(),
	});
	packageInferenceErrorCode_inputs.ReinstateErrorCodeInputSchema = z.object({
	    errorCodeId: z.string(),
	});
	
	return packageInferenceErrorCode_inputs;
}

var hasRequiredPackageDataSpec;

function requirePackageDataSpec () {
	if (hasRequiredPackageDataSpec) return PackageDataSpec;
	hasRequiredPackageDataSpec = 1;
	(function (exports) {
		var __createBinding = (PackageDataSpec && PackageDataSpec.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (PackageDataSpec && PackageDataSpec.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePackageDataSpecStatus(), exports);
		__exportStar(requireSpecHandle(), exports);
		__exportStar(requirePackageDataSpecClause(), exports);
		__exportStar(requirePackageDataSpecClause_inputs(), exports);
		__exportStar(requirePackageInferenceErrorCode(), exports);
		__exportStar(requirePackageInferenceErrorCode_inputs(), exports);
		
	} (PackageDataSpec));
	return PackageDataSpec;
}

var PackageEvidence = {};

var boundaryEvidenceRows = {};

var hasRequiredBoundaryEvidenceRows;

function requireBoundaryEvidenceRows () {
	if (hasRequiredBoundaryEvidenceRows) return boundaryEvidenceRows;
	hasRequiredBoundaryEvidenceRows = 1;
	Object.defineProperty(boundaryEvidenceRows, "__esModule", { value: true });
	boundaryEvidenceRows.BoundaryEvidenceRows = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const value_comparison_1 = requireValueComparison();
	const boundary_conditions_comparison_1 = requireBoundaryConditions_comparison();
	const signal_attributes_comparison_1 = requireSignalAttributes_comparison();
	const general_package_slices_1 = requireGeneralPackageSlices();
	const slice_key_1 = requireSliceKey();
	const slice_version_1 = requireSliceVersion();
	class BoundaryEvidenceRows {
	    static { this.BOUNDARY_COMPARISON = new boundary_conditions_comparison_1.BoundaryConditionsComparison(); }
	    static { this.SIGNAL_ATTRIBUTES_COMPARISON = new signal_attributes_comparison_1.SignalAttributesComparison(); }
	    static { this.SLICES = [
	        { kind: general_package_slices_1.BoundaryConditionKind.Amr, sliceKey: slice_key_1.SliceKey.AmrBoundaries },
	        { kind: general_package_slices_1.BoundaryConditionKind.Oc, sliceKey: slice_key_1.SliceKey.OcBoundaries },
	    ]; }
	    static of(boundary, context, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	        return BoundaryEvidenceRows.SLICES.flatMap(({ kind, sliceKey }) => (0, general_package_slices_1.buildBoundaryConditionRows)({ boundaries: [boundary], signals: context.signals, modes: context.modes }, kind, logger).map((row) => ({
	            sliceKey,
	            sliceVersion: (0, slice_version_1.sliceVersionFor)(sliceKey),
	            rowKey: BoundaryEvidenceRows.BOUNDARY_COMPARISON.rowKeyOf(row),
	            signalName: row.signalName,
	            minimum: row.minimum,
	            nominal: row.nominal,
	            maximum: row.maximum,
	        })));
	    }
	    static signalAttributesRowKey(signalName) {
	        const comparison = BoundaryEvidenceRows.SIGNAL_ATTRIBUTES_COMPARISON;
	        return (0, value_comparison_1.compositeKey)({ signalName }, comparison.alignment.keyColumns.map(String), (column, value) => comparison.normalize(column, value));
	    }
	}
	boundaryEvidenceRows.BoundaryEvidenceRows = BoundaryEvidenceRows;
	
	return boundaryEvidenceRows;
}

var evidenceAddress = {};

var evidence_errors = {};

var hasRequiredEvidence_errors;

function requireEvidence_errors () {
	if (hasRequiredEvidence_errors) return evidence_errors;
	hasRequiredEvidence_errors = 1;
	Object.defineProperty(evidence_errors, "__esModule", { value: true });
	evidence_errors.EvidenceLinkNotFoundError = evidence_errors.EvidenceLinkSliceNotSupportedError = evidence_errors.InvalidEvidenceLinkObjectError = evidence_errors.EvidenceTopicTagForbiddenError = evidence_errors.UnknownEvidenceTopicError = evidence_errors.EvidenceLocatorNotFoundError = evidence_errors.EvidenceVoterKeyError = evidence_errors.EvidenceVoterIdentityError = evidence_errors.InvalidEvidenceAddressError = void 0;
	evidence_errors.isInvalidEvidenceAddressError = isInvalidEvidenceAddressError;
	evidence_errors.isEvidenceVoterIdentityError = isEvidenceVoterIdentityError;
	evidence_errors.isEvidenceVoterKeyError = isEvidenceVoterKeyError;
	evidence_errors.isEvidenceLocatorNotFoundError = isEvidenceLocatorNotFoundError;
	evidence_errors.isUnknownEvidenceTopicError = isUnknownEvidenceTopicError;
	evidence_errors.isEvidenceTopicTagForbiddenError = isEvidenceTopicTagForbiddenError;
	evidence_errors.isInvalidEvidenceLinkObjectError = isInvalidEvidenceLinkObjectError;
	evidence_errors.isEvidenceLinkSliceNotSupportedError = isEvidenceLinkSliceNotSupportedError;
	evidence_errors.isEvidenceLinkNotFoundError = isEvidenceLinkNotFoundError;
	class InvalidEvidenceAddressError extends Error {
	    constructor(address, reason) {
	        super(`Invalid evidence address '${address}': ${reason}`);
	        this.name = this.constructor.name;
	        this.address = address;
	    }
	}
	evidence_errors.InvalidEvidenceAddressError = InvalidEvidenceAddressError;
	function isInvalidEvidenceAddressError(error) {
	    return error instanceof InvalidEvidenceAddressError;
	}
	class EvidenceVoterIdentityError extends Error {
	    constructor(agency, missingField) {
	        super(`Cannot derive an evidence voter key for agency '${agency}' without ${missingField}`);
	        this.name = this.constructor.name;
	        this.agency = agency;
	        this.missingField = missingField;
	    }
	}
	evidence_errors.EvidenceVoterIdentityError = EvidenceVoterIdentityError;
	function isEvidenceVoterIdentityError(error) {
	    return error instanceof EvidenceVoterIdentityError;
	}
	class EvidenceVoterKeyError extends Error {
	    constructor(voterKey) {
	        super(`Evidence voter key '${voterKey}' names no known voter kind`);
	        this.name = this.constructor.name;
	        this.voterKey = voterKey;
	    }
	}
	evidence_errors.EvidenceVoterKeyError = EvidenceVoterKeyError;
	function isEvidenceVoterKeyError(error) {
	    return error instanceof EvidenceVoterKeyError;
	}
	class EvidenceLocatorNotFoundError extends Error {
	    constructor(address) {
	        super(`No evidence region is recorded at '${address}'`);
	        this.name = this.constructor.name;
	        this.address = address;
	    }
	}
	evidence_errors.EvidenceLocatorNotFoundError = EvidenceLocatorNotFoundError;
	function isEvidenceLocatorNotFoundError(error) {
	    return error instanceof EvidenceLocatorNotFoundError;
	}
	class UnknownEvidenceTopicError extends Error {
	    constructor(topic) {
	        super(`Evidence topic '${topic}' is not registered`);
	        this.name = this.constructor.name;
	        this.topic = topic;
	    }
	}
	evidence_errors.UnknownEvidenceTopicError = UnknownEvidenceTopicError;
	function isUnknownEvidenceTopicError(error) {
	    return error instanceof UnknownEvidenceTopicError;
	}
	class EvidenceTopicTagForbiddenError extends Error {
	    constructor(address, topic) {
	        super(`Region '${address}' is not tagged with topic '${topic}', and this voter may not add it`);
	        this.name = this.constructor.name;
	        this.address = address;
	        this.topic = topic;
	    }
	}
	evidence_errors.EvidenceTopicTagForbiddenError = EvidenceTopicTagForbiddenError;
	function isEvidenceTopicTagForbiddenError(error) {
	    return error instanceof EvidenceTopicTagForbiddenError;
	}
	class InvalidEvidenceLinkObjectError extends Error {
	    constructor(object, reason) {
	        super(`Invalid evidence link object '${object}': ${reason}`);
	        this.name = this.constructor.name;
	        this.object = object;
	    }
	}
	evidence_errors.InvalidEvidenceLinkObjectError = InvalidEvidenceLinkObjectError;
	function isInvalidEvidenceLinkObjectError(error) {
	    return error instanceof InvalidEvidenceLinkObjectError;
	}
	class EvidenceLinkSliceNotSupportedError extends Error {
	    constructor(sliceKey) {
	        super(`Rows of slice '${sliceKey}' cannot carry evidence links`);
	        this.name = this.constructor.name;
	        this.sliceKey = sliceKey;
	    }
	}
	evidence_errors.EvidenceLinkSliceNotSupportedError = EvidenceLinkSliceNotSupportedError;
	function isEvidenceLinkSliceNotSupportedError(error) {
	    return error instanceof EvidenceLinkSliceNotSupportedError;
	}
	class EvidenceLinkNotFoundError extends Error {
	    constructor(locatorAddress) {
	        super(`Region '${locatorAddress}' is not linked to any of the selected rows`);
	        this.name = this.constructor.name;
	        this.locatorAddress = locatorAddress;
	    }
	}
	evidence_errors.EvidenceLinkNotFoundError = EvidenceLinkNotFoundError;
	function isEvidenceLinkNotFoundError(error) {
	    return error instanceof EvidenceLinkNotFoundError;
	}
	
	return evidence_errors;
}

var evidenceVote_types = {};

var hasRequiredEvidenceVote_types;

function requireEvidenceVote_types () {
	if (hasRequiredEvidenceVote_types) return evidenceVote_types;
	hasRequiredEvidenceVote_types = 1;
	Object.defineProperty(evidenceVote_types, "__esModule", { value: true });
	evidenceVote_types.EvidenceVotePolaritySchema = evidenceVote_types.EvidenceVotePolarity = evidenceVote_types.EvidenceLocatorKindSchema = evidenceVote_types.EvidenceLocatorKind = evidenceVote_types.EvidenceVoteKindSchema = evidenceVote_types.EvidenceVoteKind = void 0;
	const z = /*@__PURE__*/ requireZod();
	var EvidenceVoteKind;
	(function (EvidenceVoteKind) {
	    EvidenceVoteKind["Tag"] = "tag";
	    EvidenceVoteKind["Link"] = "link";
	})(EvidenceVoteKind || (evidenceVote_types.EvidenceVoteKind = EvidenceVoteKind = {}));
	evidenceVote_types.EvidenceVoteKindSchema = z.nativeEnum(EvidenceVoteKind);
	var EvidenceLocatorKind;
	(function (EvidenceLocatorKind) {
	    EvidenceLocatorKind["File"] = "file";
	    EvidenceLocatorKind["Page"] = "page";
	    EvidenceLocatorKind["Element"] = "element";
	    EvidenceLocatorKind["Row"] = "row";
	    EvidenceLocatorKind["Chunk"] = "chunk";
	})(EvidenceLocatorKind || (evidenceVote_types.EvidenceLocatorKind = EvidenceLocatorKind = {}));
	evidenceVote_types.EvidenceLocatorKindSchema = z.nativeEnum(EvidenceLocatorKind);
	var EvidenceVotePolarity;
	(function (EvidenceVotePolarity) {
	    EvidenceVotePolarity[EvidenceVotePolarity["For"] = 1] = "For";
	    EvidenceVotePolarity[EvidenceVotePolarity["Against"] = -1] = "Against";
	    EvidenceVotePolarity[EvidenceVotePolarity["Withdrawn"] = 0] = "Withdrawn";
	})(EvidenceVotePolarity || (evidenceVote_types.EvidenceVotePolarity = EvidenceVotePolarity = {}));
	evidenceVote_types.EvidenceVotePolaritySchema = z.nativeEnum(EvidenceVotePolarity);
	
	return evidenceVote_types;
}

var hasRequiredEvidenceAddress;

function requireEvidenceAddress () {
	if (hasRequiredEvidenceAddress) return evidenceAddress;
	hasRequiredEvidenceAddress = 1;
	Object.defineProperty(evidenceAddress, "__esModule", { value: true });
	evidenceAddress.EvidenceAddress = void 0;
	const evidence_errors_1 = requireEvidence_errors();
	const evidence_vote_types_1 = requireEvidenceVote_types();
	class EvidenceAddress {
	    static { this.SEPARATOR = '/'; }
	    static { this.FILE_SEGMENT = 'file'; }
	    static { this.PAGE_SEGMENT = 'page'; }
	    static { this.ELEMENT_SEGMENT = 'element'; }
	    static { this.ROW_SEGMENT = 'row'; }
	    static { this.CHUNK_SEGMENT = 'chunk'; }
	    static { this.POSITIVE_INTEGER = /^[1-9]\d*$/; }
	    static { this.NON_NEGATIVE_INTEGER = /^(0|[1-9]\d*)$/; }
	    static { this.GRAINS = [
	        {
	            kind: evidence_vote_types_1.EvidenceLocatorKind.Page,
	            keyword: EvidenceAddress.PAGE_SEGMENT,
	            under: [evidence_vote_types_1.EvidenceLocatorKind.File],
	            values: ['n'],
	            reserved: false,
	        },
	        {
	            kind: evidence_vote_types_1.EvidenceLocatorKind.Element,
	            keyword: EvidenceAddress.ELEMENT_SEGMENT,
	            under: [evidence_vote_types_1.EvidenceLocatorKind.Page],
	            values: ['provider', 'index'],
	            reserved: false,
	        },
	        {
	            kind: evidence_vote_types_1.EvidenceLocatorKind.Row,
	            keyword: EvidenceAddress.ROW_SEGMENT,
	            under: [evidence_vote_types_1.EvidenceLocatorKind.Element],
	            values: ['provider', 'index'],
	            reserved: false,
	        },
	        {
	            kind: evidence_vote_types_1.EvidenceLocatorKind.Chunk,
	            keyword: EvidenceAddress.CHUNK_SEGMENT,
	            under: [evidence_vote_types_1.EvidenceLocatorKind.Page, evidence_vote_types_1.EvidenceLocatorKind.Element],
	            values: ['provider', 'index'],
	            reserved: true,
	        },
	    ]; }
	    static file(fileId) {
	        const safeFileId = EvidenceAddress.token(fileId, 'file id', fileId);
	        return [EvidenceAddress.FILE_SEGMENT, safeFileId].join(EvidenceAddress.SEPARATOR);
	    }
	    static page(fileId, pageNumber) {
	        const fileAddress = EvidenceAddress.file(fileId);
	        EvidenceAddress.assertInteger(pageNumber, 1, 'page number', fileAddress);
	        return [fileAddress, EvidenceAddress.PAGE_SEGMENT, String(pageNumber)].join(EvidenceAddress.SEPARATOR);
	    }
	    static element(fileId, pageNumber, provider, elementIndex) {
	        const pageAddress = EvidenceAddress.page(fileId, pageNumber);
	        const safeProvider = EvidenceAddress.token(provider, 'provider', pageAddress);
	        EvidenceAddress.assertInteger(elementIndex, 0, 'element index', pageAddress);
	        return [pageAddress, EvidenceAddress.ELEMENT_SEGMENT, safeProvider, String(elementIndex)].join(EvidenceAddress.SEPARATOR);
	    }
	    static row(elementAddress, rowProvider, rowIndex) {
	        if (EvidenceAddress.parse(elementAddress).kind !== evidence_vote_types_1.EvidenceLocatorKind.Element) {
	            throw new evidence_errors_1.InvalidEvidenceAddressError(elementAddress, 'a row must sit under an element');
	        }
	        const safeProvider = EvidenceAddress.token(rowProvider, 'row provider', elementAddress);
	        EvidenceAddress.assertInteger(rowIndex, 0, 'row index', elementAddress);
	        return [elementAddress, EvidenceAddress.ROW_SEGMENT, safeProvider, String(rowIndex)].join(EvidenceAddress.SEPARATOR);
	    }
	    static parentOf(address) {
	        const parsed = EvidenceAddress.parse(address);
	        switch (parsed.kind) {
	            case evidence_vote_types_1.EvidenceLocatorKind.Row:
	                return EvidenceAddress.element(parsed.fileId, parsed.page, parsed.provider, parsed.elementIndex);
	            case evidence_vote_types_1.EvidenceLocatorKind.Element:
	                return EvidenceAddress.page(parsed.fileId, parsed.page);
	            case evidence_vote_types_1.EvidenceLocatorKind.Page:
	                return EvidenceAddress.file(parsed.fileId);
	            case evidence_vote_types_1.EvidenceLocatorKind.File:
	                return null;
	        }
	    }
	    static likePrefixPattern(prefix) {
	        if (!prefix.endsWith(EvidenceAddress.SEPARATOR)) {
	            throw new evidence_errors_1.InvalidEvidenceAddressError(prefix, `a prefix must end with '${EvidenceAddress.SEPARATOR}'`);
	        }
	        return `${prefix.replace(/[\\%_]/g, '\\$&')}%`;
	    }
	    static parse(address) {
	        const [fileKeyword, fileId, ...rest] = address.split(EvidenceAddress.SEPARATOR);
	        if (fileKeyword !== EvidenceAddress.FILE_SEGMENT || fileId === undefined || fileId === '') {
	            throw new evidence_errors_1.InvalidEvidenceAddressError(address, `expected '${EvidenceAddress.FILE_SEGMENT}/{fileId}'`);
	        }
	        let parsed = { kind: evidence_vote_types_1.EvidenceLocatorKind.File, fileId };
	        let remaining = rest;
	        while (remaining.length > 0) {
	            const [keyword, ...afterKeyword] = remaining;
	            const parentKind = parsed.kind;
	            const grain = EvidenceAddress.GRAINS.find((candidate) => candidate.keyword === keyword && candidate.under.includes(parentKind));
	            if (grain === undefined) {
	                throw new evidence_errors_1.InvalidEvidenceAddressError(address, `'${keyword}' cannot follow '${parentKind}'`);
	            }
	            if (grain.reserved) {
	                throw new evidence_errors_1.InvalidEvidenceAddressError(address, `'${grain.keyword}' regions are not yet addressable`);
	            }
	            const values = afterKeyword.slice(0, grain.values.length);
	            if (values.length < grain.values.length) {
	                const usage = [grain.keyword, ...grain.values.map((name) => `{${name}}`)].join(EvidenceAddress.SEPARATOR);
	                throw new evidence_errors_1.InvalidEvidenceAddressError(address, `expected '${usage}' under '${parentKind}'`);
	            }
	            parsed = EvidenceAddress.descend(parsed, grain, values, address);
	            remaining = afterKeyword.slice(grain.values.length);
	        }
	        return parsed;
	    }
	    static descend(parent, grain, values, address) {
	        const [first = '', second = ''] = values;
	        if (grain.kind === evidence_vote_types_1.EvidenceLocatorKind.Page && parent.kind === evidence_vote_types_1.EvidenceLocatorKind.File) {
	            const page = EvidenceAddress.parseInteger(first, EvidenceAddress.POSITIVE_INTEGER, 'page number', address);
	            return { ...parent, kind: evidence_vote_types_1.EvidenceLocatorKind.Page, page };
	        }
	        if (grain.kind === evidence_vote_types_1.EvidenceLocatorKind.Element && parent.kind === evidence_vote_types_1.EvidenceLocatorKind.Page) {
	            return {
	                ...parent,
	                kind: evidence_vote_types_1.EvidenceLocatorKind.Element,
	                provider: EvidenceAddress.token(first, 'provider', address),
	                elementIndex: EvidenceAddress.parseInteger(second, EvidenceAddress.NON_NEGATIVE_INTEGER, 'element index', address),
	            };
	        }
	        if (grain.kind === evidence_vote_types_1.EvidenceLocatorKind.Row && parent.kind === evidence_vote_types_1.EvidenceLocatorKind.Element) {
	            return {
	                ...parent,
	                kind: evidence_vote_types_1.EvidenceLocatorKind.Row,
	                rowProvider: EvidenceAddress.token(first, 'row provider', address),
	                rowIndex: EvidenceAddress.parseInteger(second, EvidenceAddress.NON_NEGATIVE_INTEGER, 'row index', address),
	            };
	        }
	        throw new evidence_errors_1.InvalidEvidenceAddressError(address, `'${grain.keyword}' cannot follow '${parent.kind}'`);
	    }
	    static token(value, label, context) {
	        if (value === '' || value.includes(EvidenceAddress.SEPARATOR)) {
	            throw new evidence_errors_1.InvalidEvidenceAddressError(context, `${label} '${value}' must be non-empty and free of '${EvidenceAddress.SEPARATOR}'`);
	        }
	        return value;
	    }
	    static assertInteger(value, minimum, label, context) {
	        if (!Number.isSafeInteger(value) || value < minimum) {
	            throw new evidence_errors_1.InvalidEvidenceAddressError(context, `${label} ${value} must be an integer >= ${minimum}`);
	        }
	    }
	    static parseInteger(token, shape, label, address) {
	        const value = Number(token);
	        if (!shape.test(token) || !Number.isSafeInteger(value)) {
	            throw new evidence_errors_1.InvalidEvidenceAddressError(address, `${label} '${token}' is not a canonical integer`);
	        }
	        return value;
	    }
	}
	evidenceAddress.EvidenceAddress = EvidenceAddress;
	
	return evidenceAddress;
}

var evidenceConfidence = {};

var evidenceVoterTier = {};

var hasRequiredEvidenceVoterTier;

function requireEvidenceVoterTier () {
	if (hasRequiredEvidenceVoterTier) return evidenceVoterTier;
	hasRequiredEvidenceVoterTier = 1;
	Object.defineProperty(evidenceVoterTier, "__esModule", { value: true });
	evidenceVoterTier.DECIDING_EVIDENCE_VOTER_TIERS_DESCENDING = evidenceVoterTier.EvidenceVoterTierSchema = evidenceVoterTier.EvidenceVoterTier = void 0;
	evidenceVoterTier.classifyVoterTier = classifyVoterTier;
	const z = /*@__PURE__*/ requireZod();
	const authentication_types_1 = requireAuthentication_types();
	const reviewer_role_1 = requireReviewerRole();
	var EvidenceVoterTier;
	(function (EvidenceVoterTier) {
	    EvidenceVoterTier["Pipeline"] = "pipeline";
	    EvidenceVoterTier["Trainee"] = "trainee";
	    EvidenceVoterTier["GeneralUser"] = "general_user";
	    EvidenceVoterTier["GeneralReviewer"] = "general_reviewer";
	    EvidenceVoterTier["SeniorReviewer"] = "senior_reviewer";
	    EvidenceVoterTier["ExpertReviewer"] = "expert_reviewer";
	    EvidenceVoterTier["AdminReviewer"] = "admin_reviewer";
	})(EvidenceVoterTier || (evidenceVoterTier.EvidenceVoterTier = EvidenceVoterTier = {}));
	evidenceVoterTier.EvidenceVoterTierSchema = z.nativeEnum(EvidenceVoterTier);
	evidenceVoterTier.DECIDING_EVIDENCE_VOTER_TIERS_DESCENDING = [
	    EvidenceVoterTier.AdminReviewer,
	    EvidenceVoterTier.ExpertReviewer,
	    EvidenceVoterTier.SeniorReviewer,
	    EvidenceVoterTier.GeneralReviewer,
	    EvidenceVoterTier.GeneralUser,
	];
	function classifyVoterTier(standing) {
	    if (standing.isStaff) {
	        return EvidenceVoterTier.AdminReviewer;
	    }
	    if (standing.reviewerRole === reviewer_role_1.ReviewerRole.Expert) {
	        return EvidenceVoterTier.ExpertReviewer;
	    }
	    if (standing.reviewerRole === reviewer_role_1.ReviewerRole.Senior) {
	        return EvidenceVoterTier.SeniorReviewer;
	    }
	    if (standing.memberships.some((m) => m.role === authentication_types_1.MembershipRole.Trainee)) {
	        return EvidenceVoterTier.Trainee;
	    }
	    if (standing.holdsReviewerCapability) {
	        return EvidenceVoterTier.GeneralReviewer;
	    }
	    return EvidenceVoterTier.GeneralUser;
	}
	
	return evidenceVoterTier;
}

var hasRequiredEvidenceConfidence;

function requireEvidenceConfidence () {
	if (hasRequiredEvidenceConfidence) return evidenceConfidence;
	hasRequiredEvidenceConfidence = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EvidenceConfidenceSchema = exports.EvidenceTierTallySchema = exports.EvidenceConfidenceVerdictSchema = exports.EvidenceConfidenceVerdict = void 0;
		exports.computeEvidenceConfidence = computeEvidenceConfidence;
		const z = /*@__PURE__*/ requireZod();
		const evidence_vote_types_1 = requireEvidenceVote_types();
		const evidence_voter_tier_1 = requireEvidenceVoterTier();
		var EvidenceConfidenceVerdict;
		(function (EvidenceConfidenceVerdict) {
		    EvidenceConfidenceVerdict["Confirmed"] = "confirmed";
		    EvidenceConfidenceVerdict["Possible"] = "possible";
		    EvidenceConfidenceVerdict["Rejected"] = "rejected";
		})(EvidenceConfidenceVerdict || (exports.EvidenceConfidenceVerdict = EvidenceConfidenceVerdict = {}));
		exports.EvidenceConfidenceVerdictSchema = z.nativeEnum(EvidenceConfidenceVerdict);
		exports.EvidenceTierTallySchema = z.object({
		    tier: evidence_voter_tier_1.EvidenceVoterTierSchema,
		    votesFor: z.number().int().min(0),
		    votesAgainst: z.number().int().min(0),
		});
		exports.EvidenceConfidenceSchema = z.object({
		    verdict: exports.EvidenceConfidenceVerdictSchema,
		    decidedBy: evidence_voter_tier_1.EvidenceVoterTierSchema.nullable(),
		    tallies: z.array(exports.EvidenceTierTallySchema),
		});
		const TALLIED_TIERS_DESCENDING = [
		    ...evidence_voter_tier_1.DECIDING_EVIDENCE_VOTER_TIERS_DESCENDING,
		    evidence_voter_tier_1.EvidenceVoterTier.Pipeline,
		];
		function computeEvidenceConfidence(stances) {
		    const standing = stances.filter((stance) => stance.polarity !== evidence_vote_types_1.EvidenceVotePolarity.Withdrawn);
		    const tallies = TALLIED_TIERS_DESCENDING.map((tier) => tallyTier(standing, tier)).filter((tally) => tally !== null);
		    const decider = tallies.find((tally) => evidence_voter_tier_1.DECIDING_EVIDENCE_VOTER_TIERS_DESCENDING.includes(tally.tier) && tally.votesFor !== tally.votesAgainst);
		    if (decider === undefined) {
		        return { verdict: EvidenceConfidenceVerdict.Possible, decidedBy: null, tallies };
		    }
		    const verdict = decider.votesFor > decider.votesAgainst ? EvidenceConfidenceVerdict.Confirmed : EvidenceConfidenceVerdict.Rejected;
		    return { verdict, decidedBy: decider.tier, tallies };
		}
		function tallyTier(standing, tier) {
		    const inTier = standing.filter((stance) => stance.tier === tier);
		    if (inTier.length === 0) {
		        return null;
		    }
		    return {
		        tier,
		        votesFor: inTier.filter((stance) => stance.polarity === evidence_vote_types_1.EvidenceVotePolarity.For).length,
		        votesAgainst: inTier.filter((stance) => stance.polarity === evidence_vote_types_1.EvidenceVotePolarity.Against).length,
		    };
		}
		
	} (evidenceConfidence));
	return evidenceConfidence;
}

var evidenceContracts_types = {};

var evidenceLocator_types = {};

var hasRequiredEvidenceLocator_types;

function requireEvidenceLocator_types () {
	if (hasRequiredEvidenceLocator_types) return evidenceLocator_types;
	hasRequiredEvidenceLocator_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EvidenceLocatorFactsSchema = exports.EvidenceBoundingBoxSourceSchema = exports.EvidenceBoundingBoxSource = exports.EvidenceBoundingBoxSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const evidence_vote_types_1 = requireEvidenceVote_types();
		exports.EvidenceBoundingBoxSchema = z.object({
		    ulx: z.number(),
		    uly: z.number(),
		    lrx: z.number(),
		    lry: z.number(),
		});
		var EvidenceBoundingBoxSource;
		(function (EvidenceBoundingBoxSource) {
		    EvidenceBoundingBoxSource["Ocr"] = "ocr";
		    EvidenceBoundingBoxSource["OcrTextMatch"] = "ocr_text_match";
		    EvidenceBoundingBoxSource["BboxGapFill"] = "bbox_gap_fill";
		    EvidenceBoundingBoxSource["Unplaced"] = "unplaced";
		})(EvidenceBoundingBoxSource || (exports.EvidenceBoundingBoxSource = EvidenceBoundingBoxSource = {}));
		exports.EvidenceBoundingBoxSourceSchema = z.nativeEnum(EvidenceBoundingBoxSource);
		exports.EvidenceLocatorFactsSchema = z.object({
		    address: z.string(),
		    fileId: z.string(),
		    kind: evidence_vote_types_1.EvidenceLocatorKindSchema,
		    provider: z.string().nullable(),
		    pageStart: z.number().int().min(1).nullable(),
		    pageEnd: z.number().int().min(1).nullable(),
		    bbox: exports.EvidenceBoundingBoxSchema.nullable(),
		    bboxSource: exports.EvidenceBoundingBoxSourceSchema.nullable(),
		    elementType: z.string().nullable(),
		    text: z.string().nullable(),
		    markdown: z.string().nullable(),
		});
		
	} (evidenceLocator_types));
	return evidenceLocator_types;
}

var evidenceTopic = {};

var hasRequiredEvidenceTopic;

function requireEvidenceTopic () {
	if (hasRequiredEvidenceTopic) return evidenceTopic;
	hasRequiredEvidenceTopic = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EVIDENCE_TOPIC_REGISTRY = exports.EvidenceTopicDefinitionSchema = exports.EvidenceTopicSchema = exports.EvidenceTopic = void 0;
		const z = /*@__PURE__*/ requireZod();
		var EvidenceTopic;
		(function (EvidenceTopic) {
		    EvidenceTopic["Amr"] = "amr";
		    EvidenceTopic["OperatingConditions"] = "operating_conditions";
		    EvidenceTopic["DriveModes"] = "drive_modes";
		    EvidenceTopic["DriveModesVoltageThresholds"] = "drive_modes_voltage_thresholds";
		    EvidenceTopic["Pinout"] = "pinout";
		    EvidenceTopic["PinoutTable"] = "pinout_table";
		    EvidenceTopic["PinoutDiagram"] = "pinout_diagram";
		    EvidenceTopic["PinDescriptions"] = "pin_descriptions";
		})(EvidenceTopic || (exports.EvidenceTopic = EvidenceTopic = {}));
		exports.EvidenceTopicSchema = z.nativeEnum(EvidenceTopic);
		exports.EvidenceTopicDefinitionSchema = z.object({
		    key: exports.EvidenceTopicSchema,
		    parent: exports.EvidenceTopicSchema.nullable(),
		    label: z.string(),
		    shows: z.string(),
		});
		exports.EVIDENCE_TOPIC_REGISTRY = {
		    [EvidenceTopic.Amr]: {
		        key: EvidenceTopic.Amr,
		        parent: null,
		        label: 'Absolute maximum ratings',
		        shows: 'absolute maximum ratings',
		    },
		    [EvidenceTopic.OperatingConditions]: {
		        key: EvidenceTopic.OperatingConditions,
		        parent: null,
		        label: 'Operating conditions',
		        shows: 'operating conditions',
		    },
		    [EvidenceTopic.DriveModes]: {
		        key: EvidenceTopic.DriveModes,
		        parent: null,
		        label: 'Drive modes',
		        shows: 'drive modes',
		    },
		    [EvidenceTopic.DriveModesVoltageThresholds]: {
		        key: EvidenceTopic.DriveModesVoltageThresholds,
		        parent: EvidenceTopic.DriveModes,
		        label: 'Drive-mode voltage thresholds',
		        shows: 'drive-mode voltage thresholds',
		    },
		    [EvidenceTopic.Pinout]: { key: EvidenceTopic.Pinout, parent: null, label: 'Pinout', shows: 'a pinout' },
		    [EvidenceTopic.PinoutTable]: {
		        key: EvidenceTopic.PinoutTable,
		        parent: EvidenceTopic.Pinout,
		        label: 'Pinout table',
		        shows: 'a pinout table',
		    },
		    [EvidenceTopic.PinoutDiagram]: {
		        key: EvidenceTopic.PinoutDiagram,
		        parent: EvidenceTopic.Pinout,
		        label: 'Pinout diagram',
		        shows: 'a pinout diagram',
		    },
		    [EvidenceTopic.PinDescriptions]: {
		        key: EvidenceTopic.PinDescriptions,
		        parent: null,
		        label: 'Pin descriptions',
		        shows: 'pin descriptions',
		    },
		};
		
	} (evidenceTopic));
	return evidenceTopic;
}

var hasRequiredEvidenceContracts_types;

function requireEvidenceContracts_types () {
	if (hasRequiredEvidenceContracts_types) return evidenceContracts_types;
	hasRequiredEvidenceContracts_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.LinkVoteInputSchema = exports.RowEvidenceLocatorSchema = exports.EvidenceLinkedRowSchema = exports.RowEvidenceQuerySchema = exports.EvidenceRowSelectionSchema = exports.EvidenceRowKeySelectionSchema = exports.EvidenceLineageSelectionSchema = exports.EVIDENCE_SELECTION_MAX_ROWS = exports.TagVoteInputSchema = exports.EVIDENCE_VOTE_NOTE_MAX_LENGTH = exports.EvidencePreviewSchema = exports.EvidencePreviewUnavailableReasonSchema = exports.EvidencePreviewUnavailableReason = exports.TopicEvidenceLocatorSchema = void 0;
		exports.isEvidenceLineageSelection = isEvidenceLineageSelection;
		const z = /*@__PURE__*/ requireZod();
		const slice_key_1 = requireSliceKey();
		const evidence_confidence_1 = requireEvidenceConfidence();
		const evidence_locator_types_1 = requireEvidenceLocator_types();
		const evidence_topic_1 = requireEvidenceTopic();
		const evidence_vote_types_1 = requireEvidenceVote_types();
		exports.TopicEvidenceLocatorSchema = z.object({
		    locator: evidence_locator_types_1.EvidenceLocatorFactsSchema,
		    elementIndex: z.number().int().min(0).nullable(),
		    myVote: evidence_vote_types_1.EvidenceVotePolaritySchema.nullable(),
		    confidence: evidence_confidence_1.EvidenceConfidenceSchema,
		});
		var EvidencePreviewUnavailableReason;
		(function (EvidencePreviewUnavailableReason) {
		    EvidencePreviewUnavailableReason["OcrVersionMismatch"] = "ocr_version_mismatch";
		    EvidencePreviewUnavailableReason["NotRenderable"] = "not_renderable";
		})(EvidencePreviewUnavailableReason || (exports.EvidencePreviewUnavailableReason = EvidencePreviewUnavailableReason = {}));
		exports.EvidencePreviewUnavailableReasonSchema = z.nativeEnum(EvidencePreviewUnavailableReason);
		exports.EvidencePreviewSchema = z.object({
		    base64Image: z.string().nullable(),
		    unavailableReason: exports.EvidencePreviewUnavailableReasonSchema.nullable(),
		});
		exports.EVIDENCE_VOTE_NOTE_MAX_LENGTH = 2000;
		const EvidenceVoteNoteSchema = z
		    .string()
		    .trim()
		    .min(1, { message: 'A note must not be blank.' })
		    .max(exports.EVIDENCE_VOTE_NOTE_MAX_LENGTH, {
		    message: `A note must be ${exports.EVIDENCE_VOTE_NOTE_MAX_LENGTH} characters or fewer.`,
		});
		exports.TagVoteInputSchema = z.object({
		    fileId: z.string().min(1),
		    locatorAddress: z.string().min(1),
		    topic: evidence_topic_1.EvidenceTopicSchema,
		    polarity: evidence_vote_types_1.EvidenceVotePolaritySchema,
		    note: EvidenceVoteNoteSchema.optional(),
		});
		exports.EVIDENCE_SELECTION_MAX_ROWS = 16;
		exports.EvidenceLineageSelectionSchema = z
		    .object({
		    sliceKey: slice_key_1.SliceKeySchema,
		    lineageId: z.string().min(1),
		})
		    .strict();
		exports.EvidenceRowKeySelectionSchema = z
		    .object({
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int().min(1),
		    rowKey: z.string(),
		})
		    .strict();
		exports.EvidenceRowSelectionSchema = z.union([
		    exports.EvidenceLineageSelectionSchema,
		    exports.EvidenceRowKeySelectionSchema,
		]);
		function isEvidenceLineageSelection(selection) {
		    return 'lineageId' in selection;
		}
		exports.RowEvidenceQuerySchema = z.object({
		    packageId: z.string().min(1),
		    selections: z.array(exports.EvidenceRowSelectionSchema).min(1).max(exports.EVIDENCE_SELECTION_MAX_ROWS),
		});
		exports.EvidenceLinkedRowSchema = z.object({
		    sliceKey: slice_key_1.SliceKeySchema,
		    rowKey: z.string(),
		    lineageId: z.string().nullable(),
		});
		exports.RowEvidenceLocatorSchema = z.object({
		    locator: evidence_locator_types_1.EvidenceLocatorFactsSchema,
		    container: evidence_locator_types_1.EvidenceLocatorFactsSchema.nullable(),
		    linkedRows: z.array(exports.EvidenceLinkedRowSchema),
		    myVote: evidence_vote_types_1.EvidenceVotePolaritySchema.nullable(),
		    confidence: evidence_confidence_1.EvidenceConfidenceSchema,
		});
		exports.LinkVoteInputSchema = z.object({
		    packageId: z.string().min(1),
		    locatorAddress: z.string().min(1),
		    selections: z.array(exports.EvidenceRowSelectionSchema).min(1).max(exports.EVIDENCE_SELECTION_MAX_ROWS),
		    polarity: evidence_vote_types_1.EvidenceVotePolaritySchema,
		    note: EvidenceVoteNoteSchema.optional(),
		});
		
	} (evidenceContracts_types));
	return evidenceContracts_types;
}

var evidenceLink = {};

var hasRequiredEvidenceLink;

function requireEvidenceLink () {
	if (hasRequiredEvidenceLink) return evidenceLink;
	hasRequiredEvidenceLink = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EvidenceLinkObject = exports.EVIDENCE_LINK_TOPICS = exports.DataRowAddressSchema = void 0;
		exports.evidenceLinkTopicFor = evidenceLinkTopicFor;
		const z = /*@__PURE__*/ requireZod();
		const boundary_conditions_comparison_1 = requireBoundaryConditions_comparison();
		const signal_attributes_comparison_1 = requireSignalAttributes_comparison();
		const consensus_types_1 = requireConsensus_types();
		const slice_key_1 = requireSliceKey();
		const evidence_topic_1 = requireEvidenceTopic();
		const evidence_errors_1 = requireEvidence_errors();
		exports.DataRowAddressSchema = z.object({
		    packageId: z.string().min(1),
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int().min(1),
		    rowKey: z.string(),
		    lineageId: z.string().min(1).optional(),
		});
		exports.EVIDENCE_LINK_TOPICS = {
		    [slice_key_1.SliceKey.AmrBoundaries]: evidence_topic_1.EvidenceTopic.Amr,
		    [slice_key_1.SliceKey.OcBoundaries]: evidence_topic_1.EvidenceTopic.OperatingConditions,
		    [slice_key_1.SliceKey.SignalAttributes]: evidence_topic_1.EvidenceTopic.PinDescriptions,
		};
		function evidenceLinkTopicFor(sliceKey) {
		    const topic = exports.EVIDENCE_LINK_TOPICS[sliceKey];
		    if (topic === undefined) {
		        throw new evidence_errors_1.EvidenceLinkSliceNotSupportedError(sliceKey);
		    }
		    return topic;
		}
		class EvidenceLinkObject {
		    static { this.SEPARATOR = '\x1F'; }
		    static { this.POSITIVE_INTEGER = /^[1-9]\d*$/; }
		    static { this.BOUNDARY_SHAPE = {
		        rowKeySegments: new boundary_conditions_comparison_1.BoundaryConditionsComparison().alignment.keyColumns.length,
		        takesLineage: true,
		    }; }
		    static { this.ROW_SHAPES = {
		        [slice_key_1.SliceKey.AmrBoundaries]: EvidenceLinkObject.BOUNDARY_SHAPE,
		        [slice_key_1.SliceKey.OcBoundaries]: EvidenceLinkObject.BOUNDARY_SHAPE,
		        [slice_key_1.SliceKey.SignalAttributes]: {
		            rowKeySegments: new signal_attributes_comparison_1.SignalAttributesComparison().alignment.keyColumns.length,
		            takesLineage: false,
		        },
		    }; }
		    static forRow(address) {
		        const { packageId, sliceKey, sliceVersion, rowKey, lineageId } = address;
		        const shape = EvidenceLinkObject.shapeOf(sliceKey);
		        const sep = EvidenceLinkObject.SEPARATOR;
		        const rowObject = [packageId, sliceKey, String(sliceVersion), consensus_types_1.AnchorKind.Row, rowKey].join(sep);
		        const object = lineageId === undefined ? rowObject : `${rowObject}${sep}${lineageId}`;
		        if (packageId === '' || packageId.includes(sep)) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'the package id must be non-empty and free of the separator');
		        }
		        if (!EvidenceLinkObject.POSITIVE_INTEGER.test(String(sliceVersion))) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `'${sliceVersion}' is not a slice version`);
		        }
		        if (rowKey.split(sep).length !== shape.rowKeySegments) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `a ${sliceKey} row key has ${shape.rowKeySegments} components`);
		        }
		        if (lineageId !== undefined) {
		            if (!shape.takesLineage) {
		                throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `${sliceKey} links name no parent row`);
		            }
		            if (lineageId === '' || lineageId.includes(sep)) {
		                throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'the parent row id must be non-empty and free of the separator');
		            }
		        }
		        return object;
		    }
		    static parse(object) {
		        const [packageId, sliceToken, versionToken, kind, ...rest] = object.split(EvidenceLinkObject.SEPARATOR);
		        if (packageId === undefined || packageId === '') {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'expected a package id');
		        }
		        const sliceKey = slice_key_1.SliceKeySchema.safeParse(sliceToken);
		        if (!sliceKey.success) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `'${sliceToken ?? ''}' is not a slice key`);
		        }
		        const shape = EvidenceLinkObject.ROW_SHAPES[sliceKey.data];
		        if (shape === undefined) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `${sliceKey.data} rows take no links`);
		        }
		        if (versionToken === undefined || !EvidenceLinkObject.POSITIVE_INTEGER.test(versionToken)) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `'${versionToken ?? ''}' is not a slice version`);
		        }
		        if (kind !== String(consensus_types_1.AnchorKind.Row)) {
		            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'expected a row anchor');
		        }
		        const row = {
		            packageId,
		            sliceKey: sliceKey.data,
		            sliceVersion: Number(versionToken),
		            rowKey: rest.slice(0, shape.rowKeySegments).join(EvidenceLinkObject.SEPARATOR),
		        };
		        if (rest.length === shape.rowKeySegments) {
		            return row;
		        }
		        const lineageId = rest.at(-1);
		        if (rest.length === shape.rowKeySegments + 1 && shape.takesLineage && lineageId !== undefined && lineageId !== '') {
		            return { ...row, lineageId };
		        }
		        throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `expected a ${sliceKey.data} row key of ${shape.rowKeySegments} components` +
		            (shape.takesLineage ? ', optionally followed by a parent row id' : ''));
		    }
		    static slicePrefix(packageId, sliceKey) {
		        return [packageId, sliceKey, ''].join(EvidenceLinkObject.SEPARATOR);
		    }
		    static rowPrefix(packageId, sliceKey, sliceVersion, rowKey) {
		        return `${EvidenceLinkObject.forRow({ packageId, sliceKey, sliceVersion, rowKey })}${EvidenceLinkObject.SEPARATOR}`;
		    }
		    static shapeOf(sliceKey) {
		        const shape = EvidenceLinkObject.ROW_SHAPES[sliceKey];
		        if (shape === undefined) {
		            throw new evidence_errors_1.EvidenceLinkSliceNotSupportedError(sliceKey);
		        }
		        return shape;
		    }
		}
		exports.EvidenceLinkObject = EvidenceLinkObject;
		
	} (evidenceLink));
	return evidenceLink;
}

var evidenceProvenance = {};

var hasRequiredEvidenceProvenance;

function requireEvidenceProvenance () {
	if (hasRequiredEvidenceProvenance) return evidenceProvenance;
	hasRequiredEvidenceProvenance = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EvidenceVoterKind = exports.EvidenceProvenanceSchema = exports.EvidenceExtractorVoterSchema = void 0;
		exports.voterKeyFor = voterKeyFor;
		exports.voterKindOf = voterKindOf;
		const z = /*@__PURE__*/ requireZod();
		const agentic_extractor_registry_1 = requireAgenticExtractorRegistry();
		const edit_provenance_types_1 = requireEditProvenance_types();
		const evidence_errors_1 = requireEvidence_errors();
		const evidence_voter_tier_1 = requireEvidenceVoterTier();
		exports.EvidenceExtractorVoterSchema = z.object({
		    name: z.nativeEnum(agentic_extractor_registry_1.AgenticExtractorName),
		    packageId: z.string().min(1),
		});
		exports.EvidenceProvenanceSchema = edit_provenance_types_1.EditProvenanceSchema.extend({
		    workflowId: z.string().nullable(),
		    stepName: z.string().nullable(),
		    extractor: exports.EvidenceExtractorVoterSchema.nullable(),
		    voterTier: evidence_voter_tier_1.EvidenceVoterTierSchema,
		});
		var EvidenceVoterKind;
		(function (EvidenceVoterKind) {
		    EvidenceVoterKind["User"] = "user";
		    EvidenceVoterKind["Extractor"] = "extractor";
		})(EvidenceVoterKind || (exports.EvidenceVoterKind = EvidenceVoterKind = {}));
		function voterKeyFor(provenance) {
		    if (provenance.extractor !== null) {
		        if (provenance.extractor.packageId === '') {
		            throw new evidence_errors_1.EvidenceVoterIdentityError(provenance.agency, 'packageId');
		        }
		        return `${EvidenceVoterKind.Extractor}:${provenance.extractor.name}:${provenance.extractor.packageId}`;
		    }
		    if (provenance.actorUserId === null || provenance.actorUserId === '') {
		        throw new evidence_errors_1.EvidenceVoterIdentityError(provenance.agency, 'actorUserId');
		    }
		    return `${EvidenceVoterKind.User}:${provenance.actorUserId}`;
		}
		function voterKindOf(voterKey) {
		    const kind = Object.values(EvidenceVoterKind).find((candidate) => voterKey.startsWith(`${candidate}:`));
		    if (kind === undefined) {
		        throw new evidence_errors_1.EvidenceVoterKeyError(voterKey);
		    }
		    return kind;
		}
		
	} (evidenceProvenance));
	return evidenceProvenance;
}

var hasRequiredPackageEvidence;

function requirePackageEvidence () {
	if (hasRequiredPackageEvidence) return PackageEvidence;
	hasRequiredPackageEvidence = 1;
	(function (exports) {
		var __createBinding = (PackageEvidence && PackageEvidence.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (PackageEvidence && PackageEvidence.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBoundaryEvidenceRows(), exports);
		__exportStar(requireEvidenceAddress(), exports);
		__exportStar(requireEvidenceConfidence(), exports);
		__exportStar(requireEvidenceContracts_types(), exports);
		__exportStar(requireEvidenceLink(), exports);
		__exportStar(requireEvidenceLocator_types(), exports);
		__exportStar(requireEvidenceProvenance(), exports);
		__exportStar(requireEvidenceTopic(), exports);
		__exportStar(requireEvidenceVote_types(), exports);
		__exportStar(requireEvidenceVoterTier(), exports);
		__exportStar(requireEvidence_errors(), exports);
		
	} (PackageEvidence));
	return PackageEvidence;
}

var PackageStore = {};

var filterSchemas$1 = {};

var hasRequiredFilterSchemas$1;

function requireFilterSchemas$1 () {
	if (hasRequiredFilterSchemas$1) return filterSchemas$1;
	hasRequiredFilterSchemas$1 = 1;
	Object.defineProperty(filterSchemas$1, "__esModule", { value: true });
	filterSchemas$1.subModelAccessors = filterSchemas$1.subModelFilterSchema = filterSchemas$1.differentialPairAccessors = filterSchemas$1.differentialPairFilterSchema = filterSchemas$1.boundaryAccessors = filterSchemas$1.boundaryFilterSchema = filterSchemas$1.resourceAccessors = filterSchemas$1.resourceFilterSchema = filterSchemas$1.functionAccessors = filterSchemas$1.functionFilterSchema = filterSchemas$1.signalAccessors = filterSchemas$1.signalFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	filterSchemas$1.signalFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        id: {
	            description: 'Signal id (opaque). Prefer `name` for searches.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual'],
	        },
	        name: {
	            description: 'Signal name (e.g. VCC, SDA). Searchable as text.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        description: {
	            description: 'Signal description text.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        signalDirection: {
	            description: 'Signal direction (input, output, bidirectional, etc.).',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
	        },
	        floatingBehavior: {
	            description: 'Whether the signal can be left floating (Always, Sometimes, Never, Conditional, Unspecified).',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
	        },
	        isCadstromInternal: {
	            description: 'True if the signal is a Cadstrom-internal construct (parser-only).',
	            options: { type: QueryBuilder_1.FieldType.Boolean },
	        },
	        padCount: {
	            description: 'Number of pad references mapped to this signal.',
	            options: { type: QueryBuilder_1.FieldType.Number },
	        },
	        pinCount: {
	            description: 'Number of (non-null) symbol pin references on this signal.',
	            options: { type: QueryBuilder_1.FieldType.Number },
	        },
	        driveModeCount: {
	            description: 'Number of supported drive modes declared on this signal.',
	            options: { type: QueryBuilder_1.FieldType.Number },
	        },
	        driveModeType: {
	            description: 'Drive mode type literal (e.g. push-pull, open-drain, high-z). Matches if any supported drive mode has the given type.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'contains'],
	        },
	    },
	});
	filterSchemas$1.signalAccessors = {
	    id: (s) => String(s.id),
	    name: (s) => s.name,
	    description: (s) => s.description ?? null,
	    signalDirection: (s) => s.signalDirection ?? null,
	    floatingBehavior: (s) => s.floatingBehavior ?? null,
	    isCadstromInternal: (s) => s.isCadstromInternal ?? false,
	    padCount: (s) => s.padRefs.length,
	    pinCount: (s) => s.pinRefs.filter((p) => p !== null).length,
	    driveModeCount: (s) => s.supportedDriveModes?.length ?? 0,
	    driveModeType: (s) => s.supportedDriveModes?.map((m) => m.type) ?? [],
	};
	filterSchemas$1.functionFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        name: {
	            description: 'Function name (canonical identifier on IComponentFunction).',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        functionClass: {
	            description: 'Function class identifier (e.g. bus.i2c.controller).',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        busRole: {
	            description: 'Bus role for bus functions (e.g. controller, target).',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
	        },
	        mustBeAssigned: {
	            description: 'True if this function must be assigned during resolution.',
	            options: { type: QueryBuilder_1.FieldType.Boolean },
	        },
	        resourceConsumed: {
	            description: 'Name of the resource this function consumes, if any.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        functionSignalCount: {
	            description: 'Number of function signals declared on this function.',
	            options: { type: QueryBuilder_1.FieldType.Number },
	        },
	    },
	});
	filterSchemas$1.functionAccessors = {
	    name: (f) => f.name,
	    functionClass: (f) => f.functionClass ?? null,
	    busRole: (f) => f.busRole ?? null,
	    mustBeAssigned: (f) => f.mustBeAssigned ?? false,
	    resourceConsumed: (f) => f.resourceConsumed ?? null,
	    functionSignalCount: (f) => f.functionSignals.length,
	};
	filterSchemas$1.resourceFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        name: {
	            description: 'Resource name (canonical identifier on IComponentResource).',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        mustBeConsumed: {
	            description: 'True if a function must consume this resource.',
	            options: { type: QueryBuilder_1.FieldType.Boolean },
	        },
	    },
	});
	filterSchemas$1.resourceAccessors = {
	    name: (r) => r.name,
	    mustBeConsumed: (r) => r.mustBeConsumed ?? false,
	};
	filterSchemas$1.boundaryFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        id: {
	            description: 'Boundary id.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        global: {
	            description: 'True if the boundary is global.',
	            options: { type: QueryBuilder_1.FieldType.Boolean },
	        },
	        class: {
	            description: 'Boundary class, if present.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty', 'contains'],
	        },
	    },
	});
	filterSchemas$1.boundaryAccessors = {
	    id: (b) => b.id,
	    global: (b) => b.global ?? false,
	    class: (b) => b.boundary.class ?? null,
	};
	filterSchemas$1.differentialPairFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        name: {
	            description: 'Differential pair name (canonical identifier).',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        positiveSignalId: {
	            description: 'Signal id of the positive signal.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
	        },
	        negativeSignalId: {
	            description: 'Signal id of the negative signal.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
	        },
	    },
	});
	filterSchemas$1.differentialPairAccessors = {
	    name: (d) => d.pairName,
	    positiveSignalId: (d) => String(d.positiveId),
	    negativeSignalId: (d) => String(d.negativeId),
	};
	filterSchemas$1.subModelFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        subModelId: {
	            description: 'Unique id for this submodel.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        type: {
	            description: 'Submodel type: "spice" for SPICE netlists, "sigma" for Sigma sub-models.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf'],
	        },
	    },
	});
	filterSchemas$1.subModelAccessors = {
	    subModelId: (m) => m.subModelId,
	    type: (m) => m.type,
	};
	
	return filterSchemas$1;
}

var packageDatasheetValidation = {};

var hasRequiredPackageDatasheetValidation;

function requirePackageDatasheetValidation () {
	if (hasRequiredPackageDatasheetValidation) return packageDatasheetValidation;
	hasRequiredPackageDatasheetValidation = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PackageDatasheetValidationsSchema = exports.PackageDatasheetValidationSchema = exports.PackageDatasheetReplacementSearchSchema = exports.DatasheetReplacementOutcome = exports.MATCHING_DATASHEET_VERDICT_KINDS = exports.DatasheetValidationVerdictKind = exports.DatasheetValidationOverridableStatusSchema = exports.DatasheetValidationStatus = void 0;
		exports.parsePackageDatasheetValidations = parsePackageDatasheetValidations;
		exports.effectiveDatasheetValidationStatus = effectiveDatasheetValidationStatus;
		const zod_1 = /*@__PURE__*/ requireZod();
		var DatasheetValidationStatus;
		(function (DatasheetValidationStatus) {
		    DatasheetValidationStatus["Valid"] = "valid";
		    DatasheetValidationStatus["Invalid"] = "invalid";
		    DatasheetValidationStatus["Failed"] = "failed";
		})(DatasheetValidationStatus || (exports.DatasheetValidationStatus = DatasheetValidationStatus = {}));
		exports.DatasheetValidationOverridableStatusSchema = zod_1.z.union([
		    zod_1.z.literal(DatasheetValidationStatus.Valid),
		    zod_1.z.literal(DatasheetValidationStatus.Invalid),
		]);
		var DatasheetValidationVerdictKind;
		(function (DatasheetValidationVerdictKind) {
		    DatasheetValidationVerdictKind["ExactPart"] = "exact_part";
		    DatasheetValidationVerdictKind["FamilyDatasheet"] = "family_datasheet";
		    DatasheetValidationVerdictKind["WrongPart"] = "wrong_part";
		    DatasheetValidationVerdictKind["WrongManufacturer"] = "wrong_manufacturer";
		    DatasheetValidationVerdictKind["ProductOverview"] = "product_overview";
		    DatasheetValidationVerdictKind["OtherDocument"] = "other_document";
		    DatasheetValidationVerdictKind["NotAPdf"] = "not_a_pdf";
		})(DatasheetValidationVerdictKind || (exports.DatasheetValidationVerdictKind = DatasheetValidationVerdictKind = {}));
		exports.MATCHING_DATASHEET_VERDICT_KINDS = [
		    DatasheetValidationVerdictKind.ExactPart,
		    DatasheetValidationVerdictKind.FamilyDatasheet,
		];
		var DatasheetReplacementOutcome;
		(function (DatasheetReplacementOutcome) {
		    DatasheetReplacementOutcome["Replaced"] = "replaced";
		    DatasheetReplacementOutcome["CandidateRejected"] = "candidate_rejected";
		    DatasheetReplacementOutcome["NotFound"] = "not_found";
		    DatasheetReplacementOutcome["NotAttached"] = "not_attached";
		    DatasheetReplacementOutcome["Failed"] = "failed";
		})(DatasheetReplacementOutcome || (exports.DatasheetReplacementOutcome = DatasheetReplacementOutcome = {}));
		exports.PackageDatasheetReplacementSearchSchema = zod_1.z.object({
		    outcome: zod_1.z.nativeEnum(DatasheetReplacementOutcome),
		    candidateFileId: zod_1.z.string().optional(),
		    searchedAt: zod_1.z.string(),
		});
		exports.PackageDatasheetValidationSchema = zod_1.z.object({
		    status: zod_1.z.nativeEnum(DatasheetValidationStatus),
		    reasoning: zod_1.z.string(),
		    kind: zod_1.z.nativeEnum(DatasheetValidationVerdictKind).optional(),
		    passesPdfSignatureCheck: zod_1.z.boolean().optional(),
		    workflowId: zod_1.z.string(),
		    runId: zod_1.z.string(),
		    validatedAt: zod_1.z.string(),
		    statusOverride: exports.DatasheetValidationOverridableStatusSchema.optional(),
		    overriddenBy: zod_1.z.string().optional(),
		    overriddenAt: zod_1.z.string().optional(),
		    overrideNote: zod_1.z.string().optional(),
		    replacementSearch: exports.PackageDatasheetReplacementSearchSchema.optional(),
		    replacedFileId: zod_1.z.string().optional(),
		});
		exports.PackageDatasheetValidationsSchema = zod_1.z.record(zod_1.z.string(), exports.PackageDatasheetValidationSchema);
		function parsePackageDatasheetValidations(metadata) {
		    if (metadata === null || metadata === undefined || typeof metadata !== 'object') {
		        return undefined;
		    }
		    const candidate = metadata['datasheetValidations'];
		    if (candidate === undefined) {
		        return undefined;
		    }
		    const parsed = exports.PackageDatasheetValidationsSchema.safeParse(candidate);
		    return parsed.success ? parsed.data : undefined;
		}
		function effectiveDatasheetValidationStatus(validation) {
		    return validation === undefined ? undefined : (validation.statusOverride ?? validation.status);
		}
		
	} (packageDatasheetValidation));
	return packageDatasheetValidation;
}

var packageDatasheetProvenance = {};

var hasRequiredPackageDatasheetProvenance;

function requirePackageDatasheetProvenance () {
	if (hasRequiredPackageDatasheetProvenance) return packageDatasheetProvenance;
	hasRequiredPackageDatasheetProvenance = 1;
	Object.defineProperty(packageDatasheetProvenance, "__esModule", { value: true });
	packageDatasheetProvenance.PackageDatasheetProvenanceSchema = packageDatasheetProvenance.SeriesDatasheetBasis = packageDatasheetProvenance.DatasheetProvenanceKind = void 0;
	packageDatasheetProvenance.currentDatasheetProvenance = currentDatasheetProvenance;
	const z = /*@__PURE__*/ requireZod();
	var DatasheetProvenanceKind;
	(function (DatasheetProvenanceKind) {
	    DatasheetProvenanceKind["SharedCatalogueDocument"] = "shared_catalogue_document";
	    DatasheetProvenanceKind["Series"] = "series";
	})(DatasheetProvenanceKind || (packageDatasheetProvenance.DatasheetProvenanceKind = DatasheetProvenanceKind = {}));
	var SeriesDatasheetBasis;
	(function (SeriesDatasheetBasis) {
	    SeriesDatasheetBasis["MpnStem"] = "mpn_stem";
	    SeriesDatasheetBasis["PackageFamily"] = "package_family";
	})(SeriesDatasheetBasis || (packageDatasheetProvenance.SeriesDatasheetBasis = SeriesDatasheetBasis = {}));
	packageDatasheetProvenance.PackageDatasheetProvenanceSchema = z.object({
	    kind: z.nativeEnum(DatasheetProvenanceKind),
	    fileId: z.string(),
	    seriesBasis: z.nativeEnum(SeriesDatasheetBasis).optional(),
	    sourcePackageId: z.string().optional(),
	    sourceManufacturerPartNumber: z.string().optional(),
	    matchedUrl: z.string().optional(),
	});
	function currentDatasheetProvenance(provenance, currentFileId) {
	    return provenance !== undefined && provenance.fileId === currentFileId ? provenance : undefined;
	}
	
	return packageDatasheetProvenance;
}

var packageDatasheetState = {};

var hasRequiredPackageDatasheetState;

function requirePackageDatasheetState () {
	if (hasRequiredPackageDatasheetState) return packageDatasheetState;
	hasRequiredPackageDatasheetState = 1;
	Object.defineProperty(packageDatasheetState, "__esModule", { value: true });
	packageDatasheetState.PackageDatasheetStateSchema = packageDatasheetState.DatasheetStateReason = packageDatasheetState.DatasheetState = void 0;
	const z = /*@__PURE__*/ requireZod();
	var DatasheetState;
	(function (DatasheetState) {
	    DatasheetState["Found"] = "found";
	    DatasheetState["Searching"] = "searching";
	    DatasheetState["NotFound"] = "not_found";
	    DatasheetState["NotApplicable"] = "not_applicable";
	})(DatasheetState || (packageDatasheetState.DatasheetState = DatasheetState = {}));
	var DatasheetStateReason;
	(function (DatasheetStateReason) {
	    DatasheetStateReason["AllSourcesFailed"] = "all_sources_failed";
	    DatasheetStateReason["NoSources"] = "no_sources";
	    DatasheetStateReason["AgentNotFound"] = "agent_not_found";
	    DatasheetStateReason["AllCandidatesRejected"] = "all_candidates_rejected";
	    DatasheetStateReason["Mechanical"] = "mechanical";
	    DatasheetStateReason["TestPoint"] = "test_point";
	})(DatasheetStateReason || (packageDatasheetState.DatasheetStateReason = DatasheetStateReason = {}));
	packageDatasheetState.PackageDatasheetStateSchema = z.object({
	    packageId: z.string(),
	    fileId: z.string().nullable(),
	    state: z.nativeEnum(DatasheetState).nullable(),
	    reason: z.nativeEnum(DatasheetStateReason).nullable(),
	    lastAttemptAt: z.date().nullable(),
	    nextAttemptAt: z.date().nullable(),
	    searchAttempts: z.number().int().min(0),
	});
	
	return packageDatasheetState;
}

var datasheetEnsure = {};

var hasRequiredDatasheetEnsure;

function requireDatasheetEnsure () {
	if (hasRequiredDatasheetEnsure) return datasheetEnsure;
	hasRequiredDatasheetEnsure = 1;
	Object.defineProperty(datasheetEnsure, "__esModule", { value: true });
	datasheetEnsure.DatasheetEnsureSource = datasheetEnsure.DatasheetEnsureOutcome = datasheetEnsure.DatasheetEnsureRequestReason = void 0;
	var DatasheetEnsureRequestReason;
	(function (DatasheetEnsureRequestReason) {
	    DatasheetEnsureRequestReason["PackageUsed"] = "package_used";
	    DatasheetEnsureRequestReason["RetryDue"] = "retry_due";
	    DatasheetEnsureRequestReason["OperatorRequest"] = "operator_request";
	    DatasheetEnsureRequestReason["CliRequest"] = "cli_request";
	    DatasheetEnsureRequestReason["CataloguePick"] = "catalogue_pick";
	    DatasheetEnsureRequestReason["DatasheetRejected"] = "datasheet_rejected";
	})(DatasheetEnsureRequestReason || (datasheetEnsure.DatasheetEnsureRequestReason = DatasheetEnsureRequestReason = {}));
	var DatasheetEnsureOutcome;
	(function (DatasheetEnsureOutcome) {
	    DatasheetEnsureOutcome["Attached"] = "attached";
	    DatasheetEnsureOutcome["AlreadyFound"] = "already_found";
	    DatasheetEnsureOutcome["NotFound"] = "not_found";
	    DatasheetEnsureOutcome["NotClaimed"] = "not_claimed";
	    DatasheetEnsureOutcome["CandidateFound"] = "candidate_found";
	    DatasheetEnsureOutcome["Failed"] = "failed";
	})(DatasheetEnsureOutcome || (datasheetEnsure.DatasheetEnsureOutcome = DatasheetEnsureOutcome = {}));
	var DatasheetEnsureSource;
	(function (DatasheetEnsureSource) {
	    DatasheetEnsureSource["Url"] = "url";
	    DatasheetEnsureSource["LinkedUrl"] = "linked_url";
	    DatasheetEnsureSource["Agent"] = "agent";
	    DatasheetEnsureSource["SharedCatalogueDocument"] = "shared_catalogue_document";
	    DatasheetEnsureSource["Series"] = "series";
	    DatasheetEnsureSource["None"] = "none";
	})(DatasheetEnsureSource || (datasheetEnsure.DatasheetEnsureSource = DatasheetEnsureSource = {}));
	
	return datasheetEnsure;
}

var packageStorePackageResolution = {};

var hasRequiredPackageStorePackageResolution;

function requirePackageStorePackageResolution () {
	if (hasRequiredPackageStorePackageResolution) return packageStorePackageResolution;
	hasRequiredPackageStorePackageResolution = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.UNCLASSIFIED_SIGMA_MODEL_ID = void 0;
		exports.createDefaultPackageResolution = createDefaultPackageResolution;
		exports.UNCLASSIFIED_SIGMA_MODEL_ID = 'ic.generic';
		const DEFAULT_PACKAGE_RESOLUTION = Object.freeze({
		    sigmaModelId: exports.UNCLASSIFIED_SIGMA_MODEL_ID,
		    pins: [],
		    sigmaModelParameters: {},
		    spiceModels: [],
		    subModelEditor: {},
		    modes: [],
		    boundaries: [],
		    resources: [],
		    functions: [],
		    signals: [],
		    signalDifferentialPairs: [],
		});
		function createDefaultPackageResolution() {
		    return structuredClone(DEFAULT_PACKAGE_RESOLUTION);
		}
		
	} (packageStorePackageResolution));
	return packageStorePackageResolution;
}

var packageRecordMetadata = {};

var packageUnderstandingFailure = {};

var hasRequiredPackageUnderstandingFailure;

function requirePackageUnderstandingFailure () {
	if (hasRequiredPackageUnderstandingFailure) return packageUnderstandingFailure;
	hasRequiredPackageUnderstandingFailure = 1;
	Object.defineProperty(packageUnderstandingFailure, "__esModule", { value: true });
	packageUnderstandingFailure.PackageUnderstandingFailureSchema = packageUnderstandingFailure.PackageUnderstandingFailureKind = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var PackageUnderstandingFailureKind;
	(function (PackageUnderstandingFailureKind) {
	    PackageUnderstandingFailureKind["Transient"] = "transient";
	    PackageUnderstandingFailureKind["Permanent"] = "permanent";
	})(PackageUnderstandingFailureKind || (packageUnderstandingFailure.PackageUnderstandingFailureKind = PackageUnderstandingFailureKind = {}));
	packageUnderstandingFailure.PackageUnderstandingFailureSchema = zod_1.z.object({
	    kind: zod_1.z.nativeEnum(PackageUnderstandingFailureKind),
	    fileId: zod_1.z.string().nullable(),
	    workflowId: zod_1.z.string(),
	    reason: zod_1.z.string(),
	    failedAt: zod_1.z.string(),
	});
	
	return packageUnderstandingFailure;
}

var hasRequiredPackageRecordMetadata;

function requirePackageRecordMetadata () {
	if (hasRequiredPackageRecordMetadata) return packageRecordMetadata;
	hasRequiredPackageRecordMetadata = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PackageRecordMetadataSchema = exports.SigmaModelInferenceSchema = exports.SigmaModelInferenceSource = void 0;
		exports.parsePackageRecordMetadata = parsePackageRecordMetadata;
		exports.withSigmaModelInference = withSigmaModelInference;
		exports.latestSigmaModelInference = latestSigmaModelInference;
		exports.heuristicSigmaModelParameters = heuristicSigmaModelParameters;
		exports.isSigmaModelClassificationSettled = isSigmaModelClassificationSettled;
		const z = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const package_store_package_resolution_1 = requirePackageStorePackageResolution();
		const package_understanding_failure_1 = requirePackageUnderstandingFailure();
		const package_datasheet_provenance_1 = requirePackageDatasheetProvenance();
		var SigmaModelInferenceSource;
		(function (SigmaModelInferenceSource) {
		    SigmaModelInferenceSource["HeuristicResolver"] = "heuristic_resolver";
		    SigmaModelInferenceSource["AiDatasheetClassifier"] = "ai_datasheet_classifier";
		})(SigmaModelInferenceSource || (exports.SigmaModelInferenceSource = SigmaModelInferenceSource = {}));
		exports.SigmaModelInferenceSchema = z.object({
		    sigmaModelId: z.string().min(1),
		    sigmaModelParameters: z.record(z.string(), z.unknown()).optional(),
		    source: z.nativeEnum(SigmaModelInferenceSource),
		    inferredAt: z.string(),
		});
		exports.PackageRecordMetadataSchema = z
		    .object({
		    sigmaModelInferences: z.array(exports.SigmaModelInferenceSchema).optional(),
		    understandingFailure: package_understanding_failure_1.PackageUnderstandingFailureSchema.optional(),
		    datasheetProvenance: package_datasheet_provenance_1.PackageDatasheetProvenanceSchema.optional(),
		})
		    .passthrough();
		function parsePackageRecordMetadata(value) {
		    if (value === null || value === undefined) {
		        return undefined;
		    }
		    const parsed = exports.PackageRecordMetadataSchema.safeParse(value);
		    return parsed.success ? parsed.data : undefined;
		}
		function withSigmaModelInference(metadata, inference) {
		    const others = (metadata?.sigmaModelInferences ?? []).filter((entry) => entry.source !== inference.source);
		    return {
		        ...metadata,
		        sigmaModelInferences: [...others, inference],
		    };
		}
		function latestSigmaModelInference(metadata) {
		    const inferences = metadata?.sigmaModelInferences ?? [];
		    return inferences.reduce((latest, entry) => (latest === undefined || entry.inferredAt >= latest.inferredAt ? entry : latest), undefined);
		}
		function heuristicSigmaModelParameters(metadata, sigmaModelType) {
		    const inference = metadata?.sigmaModelInferences?.find((entry) => entry.source === SigmaModelInferenceSource.HeuristicResolver && entry.sigmaModelId === sigmaModelType);
		    const parameters = {};
		    for (const [name, raw] of Object.entries(inference?.sigmaModelParameters ?? {})) {
		        const parsed = sigma_engine_shared_1.StoredValueVarianceSchema.safeParse(raw);
		        if (parsed.success && parsed.data.value !== undefined) {
		            parameters[name] = parsed.data;
		        }
		    }
		    return parameters;
		}
		function isSigmaModelClassificationSettled(sigmaModelType, inference) {
		    if (sigmaModelType !== package_store_package_resolution_1.UNCLASSIFIED_SIGMA_MODEL_ID) {
		        return true;
		    }
		    return (inference?.sigmaModelId === package_store_package_resolution_1.UNCLASSIFIED_SIGMA_MODEL_ID &&
		        inference.source === SigmaModelInferenceSource.AiDatasheetClassifier);
		}
		
	} (packageRecordMetadata));
	return packageRecordMetadata;
}

var packageResolutionDiffView = {};

var hasRequiredPackageResolutionDiffView;

function requirePackageResolutionDiffView () {
	if (hasRequiredPackageResolutionDiffView) return packageResolutionDiffView;
	hasRequiredPackageResolutionDiffView = 1;
	Object.defineProperty(packageResolutionDiffView, "__esModule", { value: true });
	packageResolutionDiffView.toResolutionDiffView = toResolutionDiffView;
	function canonicalizeKeys(value) {
	    if (Array.isArray(value)) {
	        return value.map((item) => canonicalizeKeys(item));
	    }
	    if (value !== null && typeof value === 'object') {
	        const source = value;
	        const result = {};
	        for (const key of Object.keys(source).sort()) {
	            const child = source[key];
	            if (child === undefined) {
	                continue;
	            }
	            result[key] = canonicalizeKeys(child);
	        }
	        return result;
	    }
	    return value;
	}
	function orderBy(items, key) {
	    return [...items].sort((a, b) => key(a).localeCompare(key(b)));
	}
	function stableJson(value) {
	    return JSON.stringify(canonicalizeKeys(value));
	}
	const VOLATILE_KEYS = new Set(['id', 'metadata', 'isReviewed']);
	function stripVolatile(record) {
	    const result = {};
	    for (const [key, value] of Object.entries(record)) {
	        if (VOLATILE_KEYS.has(key)) {
	            continue;
	        }
	        result[key] = value;
	    }
	    return result;
	}
	function toResolutionDiffView(resolution) {
	    const signalNameById = new Map();
	    for (const signal of resolution.signals) {
	        signalNameById.set(String(signal.id), signal.name);
	    }
	    const signalName = (ref) => {
	        if (ref === null || ref === undefined) {
	            return null;
	        }
	        return signalNameById.get(String(ref)) ?? String(ref);
	    };
	    const view = {
	        sigmaModelId: resolution.sigmaModelId,
	        signals: orderBy(resolution.signals.map(stripVolatile), (signal) => String(signal['name'] ?? '')),
	        pins: orderBy(resolution.pins.map((pin) => ({ name: pin.name, signal: signalName(pin.signalRef) })), (pin) => pin.name),
	        sigmaModelParameters: resolution.sigmaModelParameters,
	        spiceModels: orderBy(resolution.spiceModels.map(stripVolatile), stableJson),
	        modes: orderBy(resolution.modes, (mode) => String(mode.id)),
	        absoluteMaximumRatings: orderBy(resolution.absoluteMaximumRatings ?? [], (rating) => `${rating.pinName} ${rating.characteristicType}`),
	        operatingCharacteristics: orderBy(resolution.operatingCharacteristics ?? [], (characteristic) => `${characteristic.pinName} ${characteristic.characteristicType}`),
	        boundaries: orderBy(resolution.boundaries.map(stripVolatile), stableJson),
	        resources: orderBy(resolution.resources, (resource) => resource.name),
	        functions: orderBy(resolution.functions.map(stripVolatile), (fn) => String(fn['name'] ?? '')),
	        signalDifferentialPairs: orderBy(resolution.signalDifferentialPairs.map((pair) => ({
	            pairName: pair.pairName,
	            positive: signalName(pair.positiveId),
	            negative: signalName(pair.negativeId),
	            differentialVoltageSwing: pair.differentialVoltageSwing,
	        })), (pair) => pair.pairName),
	    };
	    return canonicalizeKeys(view);
	}
	
	return packageResolutionDiffView;
}

var partTiers = {};

var hasRequiredPartTiers;

function requirePartTiers () {
	if (hasRequiredPartTiers) return partTiers;
	hasRequiredPartTiers = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SPICY_POPCORN_TIER_SIGMA_MODEL_TYPES = exports.POPCORN_TIER_SIGMA_MODEL_TYPES = void 0;
		exports.isPopcornSigmaModelType = isPopcornSigmaModelType;
		exports.isSpicyPopcornSigmaModelType = isSpicyPopcornSigmaModelType;
		exports.POPCORN_TIER_SIGMA_MODEL_TYPES = new Set([
		    'capacitor.polarized',
		    'capacitor.nonpolarized',
		    'resistor.standard',
		    'resistor.variable',
		    'resistor.varistor',
		    'resistor.thermistor',
		    'inductor.standard',
		    'ferrite.standard',
		    'fuse.generic',
		    'fuse.poly',
		    'crystal',
		]);
		exports.SPICY_POPCORN_TIER_SIGMA_MODEL_TYPES = new Set([
		    'diode.generic',
		    'diode.zener',
		    'diode.led',
		    'diode.multiled',
		    'diode.tvs.bidirectional',
		    'diode.tvs.unidirectional',
		    'transistor.bjt.npn',
		    'transistor.bjt.pnp',
		    'mosfet.enhancement.ntype',
		    'mosfet.enhancement.ptype',
		]);
		function isPopcornSigmaModelType(sigmaModelType) {
		    return exports.POPCORN_TIER_SIGMA_MODEL_TYPES.has(sigmaModelType);
		}
		function isSpicyPopcornSigmaModelType(sigmaModelType) {
		    return exports.SPICY_POPCORN_TIER_SIGMA_MODEL_TYPES.has(sigmaModelType);
		}
		
	} (partTiers));
	return partTiers;
}

var popcornSigmaModelTypes = {};

var hasRequiredPopcornSigmaModelTypes;

function requirePopcornSigmaModelTypes () {
	if (hasRequiredPopcornSigmaModelTypes) return popcornSigmaModelTypes;
	hasRequiredPopcornSigmaModelTypes = 1;
	Object.defineProperty(popcornSigmaModelTypes, "__esModule", { value: true });
	popcornSigmaModelTypes.POPCORN_SIGMA_MODEL_TYPES = void 0;
	popcornSigmaModelTypes.POPCORN_SIGMA_MODEL_TYPES = [
	    'resistor.standard',
	    'resistor.variable',
	    'resistor.varistor',
	    'resistor.thermistor',
	    'capacitor.nonpolarized',
	    'capacitor.polarized',
	    'capacitor.feedthrough',
	    'inductor.standard',
	    'ferrite.standard',
	    'diode.generic',
	    'diode.zener',
	    'diode.tvs.bidirectional',
	    'diode.tvs.unidirectional',
	    'fuse.generic',
	    'fuse.poly',
	    'jumper.2h',
	    'switch.spst',
	    'crystal',
	    'antenna.chip',
	    'battery.generic',
	    'connector.test-point',
	    'connector.mechanical-contact',
	    'ground.standard',
	    'supply.standard',
	];
	
	return popcornSigmaModelTypes;
}

var parentRatingRow = {};

var hasRequiredParentRatingRow;

function requireParentRatingRow () {
	if (hasRequiredParentRatingRow) return parentRatingRow;
	hasRequiredParentRatingRow = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ParentRatingRowCollector = exports.ParentRatingRows = exports.ParentRatingRowIds = exports.ParentRatingRowIdPrefix = exports.ParentRatingRowSchema = exports.DatasheetTableRowRefSchema = exports.SourceRowAssociation = void 0;
		const z = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		var SourceRowAssociation;
		(function (SourceRowAssociation) {
		    SourceRowAssociation["Cited"] = "cited";
		    SourceRowAssociation["Associated"] = "associated";
		})(SourceRowAssociation || (exports.SourceRowAssociation = SourceRowAssociation = {}));
		exports.DatasheetTableRowRefSchema = z.object({
		    provider: z.string().nullable(),
		    pageNumber: z.number().int().min(1),
		    elementIndex: z.number().int().min(0),
		    rowIndex: z.number().int().min(0),
		    how: z.nativeEnum(SourceRowAssociation).optional(),
		});
		exports.ParentRatingRowSchema = z.object({
		    id: z.string().min(1),
		    description: z.string(),
		    symbol: z.string().nullable(),
		    min: z.string().nullable(),
		    typ: z.string().nullable(),
		    max: z.string().nullable(),
		    boundaryIds: z.array(z.string()),
		    sourceRows: z.array(exports.DatasheetTableRowRefSchema).optional(),
		});
		var ParentRatingRowIdPrefix;
		(function (ParentRatingRowIdPrefix) {
		    ParentRatingRowIdPrefix["Amr"] = "amr_row_";
		    ParentRatingRowIdPrefix["Oc"] = "oc_row_";
		})(ParentRatingRowIdPrefix || (exports.ParentRatingRowIdPrefix = ParentRatingRowIdPrefix = {}));
		class ParentRatingRowIds {
		    static { this.HASH_HEX_LENGTH = 16; }
		    static { this.RANDOM_LENGTH = 16; }
		    static async fromSourceRows(prefix, refs, ordinal) {
		        if (refs.length === 0) {
		            throw new Error('A parent rating row id can be derived only from at least one source row.');
		        }
		        const digest = await ParentRatingRowIds.sha256Hex(ParentRatingRowIds.sourceRowsKey(refs));
		        const suffix = ordinal > 0 ? `_${ordinal}` : '';
		        return prefix + digest.slice(0, ParentRatingRowIds.HASH_HEX_LENGTH) + suffix;
		    }
		    static random(prefix) {
		        return prefix + sigma_engine_shared_1.Utils.randomAlphaStr(ParentRatingRowIds.RANDOM_LENGTH);
		    }
		    static async forRows(prefix, rows) {
		        const ordinals = new Map();
		        const seen = new Map();
		        for (const row of [...rows].sort((a, b) => a.position - b.position)) {
		            if (row.sourceRows.length === 0) {
		                continue;
		            }
		            const key = ParentRatingRowIds.sourceRowsKey(row.sourceRows);
		            const ordinal = seen.get(key) ?? 0;
		            seen.set(key, ordinal + 1);
		            ordinals.set(row, ordinal);
		        }
		        return Promise.all(rows.map(async (row) => {
		            const ordinal = ordinals.get(row);
		            const id = ordinal === undefined ?
		                ParentRatingRowIds.random(prefix)
		                : await ParentRatingRowIds.fromSourceRows(prefix, row.sourceRows, ordinal);
		            return { row, id };
		        }));
		    }
		    static sourceRowsKey(refs) {
		        const parts = refs.map(({ provider, pageNumber, elementIndex, rowIndex }) => [provider ?? '', pageNumber, elementIndex, rowIndex].join('|'));
		        return [...new Set(parts)].sort().join('\n');
		    }
		    static async sha256Hex(input) {
		        const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
		        return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
		    }
		}
		exports.ParentRatingRowIds = ParentRatingRowIds;
		class ParentRatingRows {
		    static pruneBoundaryIds(rows, boundaries) {
		        if (rows === undefined) {
		            return undefined;
		        }
		        const existing = new Set(boundaries.map((boundary) => boundary.id));
		        const pruned = rows.map((row) => {
		            const boundaryIds = row.boundaryIds.filter((id) => existing.has(id));
		            return boundaryIds.length === row.boundaryIds.length ? row : { ...row, boundaryIds };
		        });
		        return pruned.every((row, index) => row === rows[index]) ? rows : pruned;
		    }
		}
		exports.ParentRatingRows = ParentRatingRows;
		class ParentRatingRowCollector {
		    constructor() {
		        this.rows = new Map();
		    }
		    record(origin, boundaryIds) {
		        if (origin === undefined) {
		            return;
		        }
		        const entry = this.rows.get(origin.id) ?? { origin, boundaryIds: [] };
		        entry.boundaryIds.push(...boundaryIds);
		        this.rows.set(origin.id, entry);
		    }
		    toRows() {
		        return [...this.rows.values()]
		            .filter((entry) => entry.boundaryIds.length > 0)
		            .sort((a, b) => a.origin.position - b.origin.position)
		            .map(({ origin, boundaryIds }) => {
		            const { position, ...row } = origin;
		            return { ...row, boundaryIds };
		        });
		    }
		}
		exports.ParentRatingRowCollector = ParentRatingRowCollector;
		
	} (parentRatingRow));
	return parentRatingRow;
}

var hasRequiredPackageStore;

function requirePackageStore () {
	if (hasRequiredPackageStore) return PackageStore;
	hasRequiredPackageStore = 1;
	(function (exports) {
		var __createBinding = (PackageStore && PackageStore.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (PackageStore && PackageStore.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireFilterSchemas$1(), exports);
		__exportStar(requirePackageDatasheetValidation(), exports);
		__exportStar(requirePackageDatasheetProvenance(), exports);
		__exportStar(requirePackageDatasheetState(), exports);
		__exportStar(requireDatasheetEnsure(), exports);
		__exportStar(requireArraySubmodels(), exports);
		__exportStar(requirePackageStorePackageResolution(), exports);
		__exportStar(requirePackageRecordMetadata(), exports);
		__exportStar(requirePackageUnderstandingFailure(), exports);
		__exportStar(requirePackageResolutionDiffView(), exports);
		__exportStar(requirePartTiers(), exports);
		__exportStar(requirePopcornSigmaModelTypes(), exports);
		__exportStar(requireParentRatingRow(), exports);
		
	} (PackageStore));
	return PackageStore;
}

var ProjectModel = {};

var filterSchemas = {};

var hasRequiredFilterSchemas;

function requireFilterSchemas () {
	if (hasRequiredFilterSchemas) return filterSchemas;
	hasRequiredFilterSchemas = 1;
	Object.defineProperty(filterSchemas, "__esModule", { value: true });
	filterSchemas.netConnectionAccessors = filterSchemas.netConnectionFilterSchema = filterSchemas.netAccessors = filterSchemas.netFilterSchema = filterSchemas.moduleAccessors = filterSchemas.moduleFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	filterSchemas.moduleFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        ref: {
	            description: 'Module instance id — NOT the reference designator. Filter by `name` when searching for a reference designator like U1 or R3.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        name: {
	            description: 'Reference designator (e.g. U1, R3). This is the human-readable label, not the instance id.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        designId: {
	            description: 'Design containing the module.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual'],
	        },
	        designModelType: {
	            description: 'Design model type: "pcb" or "schematic".',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual'],
	        },
	        componentRef: {
	            description: 'Ref of the component this module is an instance of.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        componentName: {
	            description: 'Name of the component this module is an instance of.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        componentModelId: {
	            description: 'Model ID of the component this module is an instance of.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        projectModuleName: {
	            description: 'Name of the project-level module group this instance belongs to.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        pinCount: {
	            description: 'Number of pins on this module instance.',
	            options: { type: QueryBuilder_1.FieldType.Number },
	        },
	    },
	});
	filterSchemas.moduleAccessors = {
	    ref: (e) => e.ref,
	    name: (e) => e.name,
	    designId: (e) => e.designId,
	    designModelType: (e) => e.designModelType,
	    componentRef: (e) => e.componentRef,
	    componentName: (e) => e.componentName ?? null,
	    componentModelId: (e) => e.componentModelId ?? null,
	    projectModuleName: (e) => e.projectModuleName,
	    pinCount: (e) => e.pinCount,
	};
	filterSchemas.netFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        name: { description: 'Net name (e.g. VCC, GND, SDA).', options: { type: QueryBuilder_1.FieldType.String } },
	        designId: {
	            description: 'Design containing a connection on the net. A net can span multiple designs; filter matches any.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'contains'],
	        },
	        moduleRef: {
	            description: 'Connection module ref (instance id) — NOT the reference designator. Matches nets that connect to any module with the given instance id. If you only know the reference designator (e.g. U1), resolve it to a ref via listModules first.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'contains'],
	        },
	        isGlobal: {
	            description: 'True if the net is marked global in at least one design.',
	            options: { type: QueryBuilder_1.FieldType.Boolean },
	        },
	        isHierarchical: {
	            description: 'True if the net is marked hierarchical in at least one design.',
	            options: { type: QueryBuilder_1.FieldType.Boolean },
	        },
	        connectionCount: {
	            description: 'Total number of (moduleRef, pinRef) connection pairs on the net, deduplicated across designs.',
	            options: { type: QueryBuilder_1.FieldType.Number },
	        },
	    },
	});
	filterSchemas.netAccessors = {
	    name: (e) => e.name,
	    designId: (e) => e.designIds,
	    moduleRef: (e) => e.connections.map((c) => c.moduleRef),
	    isGlobal: (e) => e.isGlobal,
	    isHierarchical: (e) => e.isHierarchical,
	    connectionCount: (e) => e.connectionCount,
	};
	filterSchemas.netConnectionFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        designId: {
	            description: 'Design the connection belongs to.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual'],
	        },
	        moduleRef: {
	            description: 'Module instance id on the connection — NOT the reference designator. If you only know the reference designator (e.g. U1), filter by `moduleName` instead.',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        moduleName: {
	            description: 'Reference designator on the connection (e.g. U1, R3).',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	        pinRef: {
	            description: 'Pin identifier on the module (e.g. 1, 2, VCC, GND).',
	            options: { type: QueryBuilder_1.FieldType.String },
	        },
	    },
	});
	filterSchemas.netConnectionAccessors = {
	    designId: (c) => c.designId,
	    moduleRef: (c) => c.moduleRef,
	    moduleName: (c) => c.moduleName,
	    pinRef: (c) => c.pinRef,
	};
	
	return filterSchemas;
}

var hasRequiredProjectModel;

function requireProjectModel () {
	if (hasRequiredProjectModel) return ProjectModel;
	hasRequiredProjectModel = 1;
	(function (exports) {
		var __createBinding = (ProjectModel && ProjectModel.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ProjectModel && ProjectModel.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireFilterSchemas(), exports);
		
	} (ProjectModel));
	return ProjectModel;
}

var RequirementsV2 = {};

var assertionResultFilterSchema = {};

var designObjectFilterWhere = {};

var hasRequiredDesignObjectFilterWhere;

function requireDesignObjectFilterWhere () {
	if (hasRequiredDesignObjectFilterWhere) return designObjectFilterWhere;
	hasRequiredDesignObjectFilterWhere = 1;
	Object.defineProperty(designObjectFilterWhere, "__esModule", { value: true });
	designObjectFilterWhere.buildDesignObjectWhere = buildDesignObjectWhere;
	const QueryBuilder_1 = requireQueryBuilder();
	function parseDesignObjectValue(value) {
	    if (value === undefined || value === '') {
	        throw new Error('Design-object filter requires a value.');
	    }
	    const parsed = JSON.parse(value);
	    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
	        throw new Error('Design-object filter value must be a JSON object.');
	    }
	    const candidate = parsed;
	    if (typeof candidate.type !== 'string') {
	        throw new Error('Design-object filter value requires a string type.');
	    }
	    return {
	        type: candidate.type,
	        net: typeof candidate.net === 'string' ? candidate.net : null,
	        module: typeof candidate.module === 'string' ? candidate.module : null,
	        component: typeof candidate.component === 'string' ? candidate.component : null,
	        signal: typeof candidate.signal === 'string' ? candidate.signal : null,
	    };
	}
	function buildDesignObjectIdentityWhere(value) {
	    const parsed = parseDesignObjectValue(value);
	    return {
	        type: parsed.type,
	        net: parsed.net,
	        module: parsed.module,
	        component: parsed.component,
	        signal: parsed.signal,
	    };
	}
	function buildDesignObjectMatch(value) {
	    return {
	        logEntryLinks: {
	            some: {
	                logEntry: {
	                    designObjectLinks: {
	                        some: { buildDesignObject: buildDesignObjectIdentityWhere(value) },
	                    },
	                },
	            },
	        },
	    };
	}
	function buildDesignObjectAnyMatch(value) {
	    const values = (0, QueryBuilder_1.parseIsAnyOfValues)(value);
	    return {
	        logEntryLinks: {
	            some: {
	                logEntry: {
	                    designObjectLinks: {
	                        some: { buildDesignObject: { OR: values.map(buildDesignObjectIdentityWhere) } },
	                    },
	                },
	            },
	        },
	    };
	}
	function buildDesignObjectNoMatch(value) {
	    return {
	        logEntryLinks: {
	            none: {
	                logEntry: {
	                    designObjectLinks: {
	                        some: { buildDesignObject: buildDesignObjectIdentityWhere(value) },
	                    },
	                },
	            },
	        },
	    };
	}
	function buildDesignObjectNoneMatch(value) {
	    const values = (0, QueryBuilder_1.parseIsAnyOfValues)(value);
	    return {
	        logEntryLinks: {
	            none: {
	                logEntry: {
	                    designObjectLinks: {
	                        some: { buildDesignObject: { OR: values.map(buildDesignObjectIdentityWhere) } },
	                    },
	                },
	            },
	        },
	    };
	}
	function buildDesignObjectPresence(isPresent) {
	    return {
	        logEntryLinks: {
	            [isPresent ? 'some' : 'none']: {
	                logEntry: {
	                    designObjectLinks: { some: {} },
	                },
	            },
	        },
	    };
	}
	function buildDesignObjectWhere(operator, value) {
	    switch (operator) {
	        case 'is':
	        case 'equals':
	            return buildDesignObjectMatch(value);
	        case 'not':
	        case 'doesNotEqual':
	            return buildDesignObjectNoMatch(value);
	        case 'isAnyOf':
	            return buildDesignObjectAnyMatch(value);
	        case 'isNoneOf':
	            return buildDesignObjectNoneMatch(value);
	        case 'isEmpty':
	            return buildDesignObjectPresence(false);
	        case 'isNotEmpty':
	            return buildDesignObjectPresence(true);
	        default:
	            return {};
	    }
	}
	
	return designObjectFilterWhere;
}

var hasRequiredAssertionResultFilterSchema;

function requireAssertionResultFilterSchema () {
	if (hasRequiredAssertionResultFilterSchema) return assertionResultFilterSchema;
	hasRequiredAssertionResultFilterSchema = 1;
	Object.defineProperty(assertionResultFilterSchema, "__esModule", { value: true });
	assertionResultFilterSchema.assertionResultFilterSchema = void 0;
	assertionResultFilterSchema.buildHasFindingWhere = buildHasFindingWhere;
	const QueryBuilder_1 = requireQueryBuilder();
	const design_object_filter_where_1 = requireDesignObjectFilterWhere();
	function buildHasFindingWhere(hasFinding) {
	    return hasFinding ? { findingLinks: { some: {} } } : { findingLinks: { none: {} } };
	}
	function buildHasFindingFilterWhere(operator, value) {
	    const wantsLinks = operator === 'isNotEmpty' ||
	        ((operator === 'is' || operator === 'equals') && value === 'true') ||
	        ((operator === 'not' || operator === 'doesNotEqual') && value === 'false');
	    const wantsNoLinks = operator === 'isEmpty' ||
	        ((operator === 'is' || operator === 'equals') && value === 'false') ||
	        ((operator === 'not' || operator === 'doesNotEqual') && value === 'true');
	    if (wantsLinks) {
	        return { findingLinks: { some: {} } };
	    }
	    if (wantsNoLinks) {
	        return { findingLinks: { none: {} } };
	    }
	    return {};
	}
	assertionResultFilterSchema.assertionResultFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        requirementDefinitionId: {
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	            description: 'ID of the parent requirement definition (e.g. cadstrom.eif).',
	        },
	        assertionDefinitionId: {
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	            description: 'ID of the assertion definition that produced this row.',
	        },
	        resultSlug: {
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	            description: 'Cross-requirement group identity (e.g. "net:NET_VCC"). Multiple assertion definitions can emit the same slug; they collapse into one UI row.',
	        },
	        severity: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Result severity.',
	        },
	        workflowState: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Workflow state.',
	        },
	        message: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Short user-facing message rolled up from the latest contributing log entry.',
	        },
	        description: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Long-form description rolled up from the latest contributing log entry.',
	        },
	        designObject: {
	            options: { type: QueryBuilder_1.FieldType.Custom, buildWhere: design_object_filter_where_1.buildDesignObjectWhere },
	            allowedOperators: ['is', 'not', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
	            description: 'Exact transitive design object linked through the result log entries.',
	        },
	        hasFinding: {
	            options: { type: QueryBuilder_1.FieldType.Custom, buildWhere: buildHasFindingFilterWhere },
	            allowedOperators: ['is', 'not', 'equals', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
	            description: 'Whether the assertion result is linked to at least one Requirements V2 finding.',
	        },
	        continuityKeyType: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Continuity key type.',
	        },
	        continuityKey: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Opaque continuity value, typed by continuityKeyType.',
	        },
	        latestLogEntryAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Timestamp of the most recent contributing log entry (sortable).',
	        },
	        createdAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Row creation timestamp.',
	        },
	        updatedAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Row update timestamp.',
	        },
	    },
	});
	
	return assertionResultFilterSchema;
}

var buildDesignObjectFilterSchema = {};

var hasRequiredBuildDesignObjectFilterSchema;

function requireBuildDesignObjectFilterSchema () {
	if (hasRequiredBuildDesignObjectFilterSchema) return buildDesignObjectFilterSchema;
	hasRequiredBuildDesignObjectFilterSchema = 1;
	Object.defineProperty(buildDesignObjectFilterSchema, "__esModule", { value: true });
	buildDesignObjectFilterSchema.buildDesignObjectFilterSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const QueryBuilder_1 = requireQueryBuilder();
	const designObjectTypeValues = { ...sigma_engine_shared_1.DesignObjectType };
	buildDesignObjectFilterSchema.buildDesignObjectFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        type: {
	            options: {
	                type: QueryBuilder_1.FieldType.String,
	                values: designObjectTypeValues,
	                nonNullable: true,
	            },
	            allowedOperators: ['equals', 'is', 'doesNotEqual', 'not', 'isAnyOf', 'isNoneOf'],
	            description: 'DesignObject category — net | module | component | netconn | custom.',
	        },
	        net: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Net name.',
	        },
	        module: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Module path.',
	        },
	        component: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Component reference designator.',
	        },
	        signal: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Component signal pin name.',
	        },
	        customTypeName: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: "Custom subject type name (e.g. 'cadstrom.bus'); only on custom rows.",
	        },
	        subjectName: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Custom subject display name; only on custom rows.',
	        },
	    },
	});
	
	return buildDesignObjectFilterSchema;
}

var definitionFilterSchema = {};

var hasRequiredDefinitionFilterSchema;

function requireDefinitionFilterSchema () {
	if (hasRequiredDefinitionFilterSchema) return definitionFilterSchema;
	hasRequiredDefinitionFilterSchema = 1;
	Object.defineProperty(definitionFilterSchema, "__esModule", { value: true });
	definitionFilterSchema.requirementDefinitionFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	definitionFilterSchema.requirementDefinitionFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        slug: {
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	            description: 'Base slug for the requirement definition.',
	        },
	        slugPrefix: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Explicit result-slug display prefix for the requirement definition.',
	        },
	        description: {
	            options: { type: QueryBuilder_1.FieldType.String, nonNullable: true },
	            description: 'Free-text description of the requirement.',
	        },
	        createdAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Row creation timestamp.',
	        },
	        updatedAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Row update timestamp.',
	        },
	    },
	});
	
	return definitionFilterSchema;
}

var findingFilterSchema = {};

var hasRequiredFindingFilterSchema;

function requireFindingFilterSchema () {
	if (hasRequiredFindingFilterSchema) return findingFilterSchema;
	hasRequiredFindingFilterSchema = 1;
	Object.defineProperty(findingFilterSchema, "__esModule", { value: true });
	findingFilterSchema.findingFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	const design_object_filter_where_1 = requireDesignObjectFilterWhere();
	findingFilterSchema.findingFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        findingNumber: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Per-owner finding sequence number rendered as FINDING-N.',
	        },
	        severity: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Projected severity for the finding.',
	        },
	        workflowState: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Projected workflow state.',
	        },
	        message: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Latest projected user-facing message.',
	        },
	        description: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Latest projected long-form description.',
	        },
	        designObject: {
	            options: { type: QueryBuilder_1.FieldType.Custom, buildWhere: design_object_filter_where_1.buildDesignObjectWhere },
	            allowedOperators: ['is', 'not', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
	            description: 'Exact transitive design object linked through the finding log entries.',
	        },
	        latestLogEntryAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Timestamp of the most recent contributing log entry (sortable).',
	        },
	        createdAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Row creation timestamp.',
	        },
	        updatedAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Row update timestamp.',
	        },
	    },
	});
	
	return findingFilterSchema;
}

var logEntryFilterSchema = {};

var hasRequiredLogEntryFilterSchema;

function requireLogEntryFilterSchema () {
	if (hasRequiredLogEntryFilterSchema) return logEntryFilterSchema;
	hasRequiredLogEntryFilterSchema = 1;
	Object.defineProperty(logEntryFilterSchema, "__esModule", { value: true });
	logEntryFilterSchema.requirementLogEntryFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	logEntryFilterSchema.requirementLogEntryFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        entryType: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'The kind of log entry.',
	        },
	        authorRole: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Author role: system (service writes), admin (staff), or user (customer).',
	        },
	        severity: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Severity contribution from this entry, if any.',
	        },
	        workflowState: {
	            options: { type: QueryBuilder_1.FieldType.Number },
	            description: 'Workflow state contribution from this entry, if any.',
	        },
	        message: {
	            options: { type: QueryBuilder_1.FieldType.String },
	            description: 'Plain-text message body.',
	        },
	        createdAt: {
	            options: { type: QueryBuilder_1.FieldType.Date },
	            description: 'Creation timestamp (the canonical timeline ordering key).',
	        },
	    },
	});
	
	return logEntryFilterSchema;
}

var hasRequiredRequirementsV2;

function requireRequirementsV2 () {
	if (hasRequiredRequirementsV2) return RequirementsV2;
	hasRequiredRequirementsV2 = 1;
	(function (exports) {
		var __createBinding = (RequirementsV2 && RequirementsV2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (RequirementsV2 && RequirementsV2.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAssertionResultFilterSchema(), exports);
		__exportStar(requireBuildDesignObjectFilterSchema(), exports);
		__exportStar(requireDefinitionFilterSchema(), exports);
		__exportStar(requireFindingFilterSchema(), exports);
		__exportStar(requireLogEntryFilterSchema(), exports);
		
	} (RequirementsV2));
	return RequirementsV2;
}

var ServiceLocations = {};

var ServiceLocation = {};

var hasRequiredServiceLocation;

function requireServiceLocation () {
	if (hasRequiredServiceLocation) return ServiceLocation;
	hasRequiredServiceLocation = 1;
	Object.defineProperty(ServiceLocation, "__esModule", { value: true });
	ServiceLocation.parseLocationString = parseLocationString;
	ServiceLocation.createLocationString = createLocationString;
	ServiceLocation.joinLocationStringPaths = joinLocationStringPaths;
	ServiceLocation.buildOwnedBlobLocation = buildOwnedBlobLocation;
	function parseLocationString(location, optionalSource = false) {
	    const matches = /^(?<source>.+?):(?<path>.*)$/.exec(location);
	    if (matches === null) {
	        if (optionalSource) {
	            return {
	                source: null,
	                path: location,
	            };
	        }
	        throw new Error("Location paths must be in 'source:path' format.");
	    }
	    return {
	        source: matches.groups['source'],
	        path: matches.groups['path'],
	    };
	}
	function createLocationString(source, path1, ...paths) {
	    return `${source}:${[path1, ...paths].join('/')}`;
	}
	function joinLocationStringPaths(location, ...paths) {
	    const { source, path } = parseLocationString(location);
	    return createLocationString(source, path, ...paths);
	}
	function buildOwnedBlobLocation(ownedLocationSource, ownerId, relativePath) {
	    return createLocationString(ownedLocationSource, `${ownerId}/${relativePath}`);
	}
	
	return ServiceLocation;
}

var hasRequiredServiceLocations;

function requireServiceLocations () {
	if (hasRequiredServiceLocations) return ServiceLocations;
	hasRequiredServiceLocations = 1;
	(function (exports) {
		var __createBinding = (ServiceLocations && ServiceLocations.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ServiceLocations && ServiceLocations.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireServiceLocation(), exports);
		
	} (ServiceLocations));
	return ServiceLocations;
}

var SignalMapping = {};

var automapSignals = {};

var fuzzy = {};

var types$2;
var hasRequiredTypes$2;

function requireTypes$2 () {
	if (hasRequiredTypes$2) return types$2;
	hasRequiredTypes$2 = 1;
	types$2 = {
	  Other: 0,
	  CR: 1 << 0,
	  LF: 1 << 1,
	  Control: 1 << 2,
	  Extend: 1 << 3,
	  ZWJ: 1 << 4,
	  Regional_Indicator: 1 << 5,
	  Prepend: 1 << 6,
	  SpacingMark: 1 << 7,
	  L: 1 << 8,
	  V: 1 << 9,
	  T: 1 << 10,
	  LV: 1 << 11,
	  LVT: 1 << 12,
	  Extended_Pictographic: 1 << 13,
	  InCB_Linker: 1 << 14,
	  InCB_Consonant: 1 << 15,
	  InCB_Extend: 1 << 16,
	};
	return types$2;
}

var data$2 = "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";
var require$$1 = {
	data: data$2
};

var data$1 = "AAACAAAAAACAOAAAAbYBSf7t2S1IBEEYBuDVDZ7FYrQMNsFiu3hgEYOI0SCXRIUrB8JhEZtgs5gEg1GMFk02m82oGI02m+9xezCOczv/uwv3fvAwc/PzfXOzcdqzWdaBDdiGPdiHdjE+DS3RNDuCfsn8idQ/g3OH3BdwKf0e96/gumTfYcncLdzBPTzAo+RZ+f0Cr/AG7/AJX4738x1wtz9FO5PX/50n6UXMNdfg/0lERERERERERERERETpdedHBvDRql4nq0cXtW9af98qdRby0Vvp8K4W0V+C5Xw0t4J2bfjeBp3cnEu1brnnCTYNa7eKdz91XP7WO9Lb4GqRb7cY6xbtAdqeVOsY/QGcevw/tb6OT85YhvfKYEx9CMuxKsKnrs+eJtVInVvHJ0eVYVvTZk2siFVLOCjb61PTZX3MdVWEyP7fjzpmMxdzTyq2Ebue6x61nXRGnzndWpf1an7dXmGYE4Y1ptqqKsK1nu26Ju0ty+maV2Rpvk+qnDZjKUIobUiesdAQE/jmCTmHmsskpFZsVYbtmXRcaoSGUPomunW2derQhDPFjtT1Q/eb8vnm990fq35oHVt11bU9m89c7DNI8Qs=";
var require$$2 = {
	data: data$1
};

var data = "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";
var require$$3 = {
	data: data
};

var tinyInflate;
var hasRequiredTinyInflate;

function requireTinyInflate () {
	if (hasRequiredTinyInflate) return tinyInflate;
	hasRequiredTinyInflate = 1;
	var TINF_OK = 0;
	var TINF_DATA_ERROR = -3;

	function Tree() {
	  this.table = new Uint16Array(16);   /* table of code length counts */
	  this.trans = new Uint16Array(288);  /* code -> symbol translation table */
	}

	function Data(source, dest) {
	  this.source = source;
	  this.sourceIndex = 0;
	  this.tag = 0;
	  this.bitcount = 0;
	  
	  this.dest = dest;
	  this.destLen = 0;
	  
	  this.ltree = new Tree();  /* dynamic length/symbol tree */
	  this.dtree = new Tree();  /* dynamic distance tree */
	}

	/* --------------------------------------------------- *
	 * -- uninitialized global data (static structures) -- *
	 * --------------------------------------------------- */

	var sltree = new Tree();
	var sdtree = new Tree();

	/* extra bits and base tables for length codes */
	var length_bits = new Uint8Array(30);
	var length_base = new Uint16Array(30);

	/* extra bits and base tables for distance codes */
	var dist_bits = new Uint8Array(30);
	var dist_base = new Uint16Array(30);

	/* special ordering of code length codes */
	var clcidx = new Uint8Array([
	  16, 17, 18, 0, 8, 7, 9, 6,
	  10, 5, 11, 4, 12, 3, 13, 2,
	  14, 1, 15
	]);

	/* used by tinf_decode_trees, avoids allocations every call */
	var code_tree = new Tree();
	var lengths = new Uint8Array(288 + 32);

	/* ----------------------- *
	 * -- utility functions -- *
	 * ----------------------- */

	/* build extra bits and base tables */
	function tinf_build_bits_base(bits, base, delta, first) {
	  var i, sum;

	  /* build bits table */
	  for (i = 0; i < delta; ++i) bits[i] = 0;
	  for (i = 0; i < 30 - delta; ++i) bits[i + delta] = i / delta | 0;

	  /* build base table */
	  for (sum = first, i = 0; i < 30; ++i) {
	    base[i] = sum;
	    sum += 1 << bits[i];
	  }
	}

	/* build the fixed huffman trees */
	function tinf_build_fixed_trees(lt, dt) {
	  var i;

	  /* build fixed length tree */
	  for (i = 0; i < 7; ++i) lt.table[i] = 0;

	  lt.table[7] = 24;
	  lt.table[8] = 152;
	  lt.table[9] = 112;

	  for (i = 0; i < 24; ++i) lt.trans[i] = 256 + i;
	  for (i = 0; i < 144; ++i) lt.trans[24 + i] = i;
	  for (i = 0; i < 8; ++i) lt.trans[24 + 144 + i] = 280 + i;
	  for (i = 0; i < 112; ++i) lt.trans[24 + 144 + 8 + i] = 144 + i;

	  /* build fixed distance tree */
	  for (i = 0; i < 5; ++i) dt.table[i] = 0;

	  dt.table[5] = 32;

	  for (i = 0; i < 32; ++i) dt.trans[i] = i;
	}

	/* given an array of code lengths, build a tree */
	var offs = new Uint16Array(16);

	function tinf_build_tree(t, lengths, off, num) {
	  var i, sum;

	  /* clear code length count table */
	  for (i = 0; i < 16; ++i) t.table[i] = 0;

	  /* scan symbol lengths, and sum code length counts */
	  for (i = 0; i < num; ++i) t.table[lengths[off + i]]++;

	  t.table[0] = 0;

	  /* compute offset table for distribution sort */
	  for (sum = 0, i = 0; i < 16; ++i) {
	    offs[i] = sum;
	    sum += t.table[i];
	  }

	  /* create code->symbol translation table (symbols sorted by code) */
	  for (i = 0; i < num; ++i) {
	    if (lengths[off + i]) t.trans[offs[lengths[off + i]]++] = i;
	  }
	}

	/* ---------------------- *
	 * -- decode functions -- *
	 * ---------------------- */

	/* get one bit from source stream */
	function tinf_getbit(d) {
	  /* check if tag is empty */
	  if (!d.bitcount--) {
	    /* load next tag */
	    d.tag = d.source[d.sourceIndex++];
	    d.bitcount = 7;
	  }

	  /* shift bit out of tag */
	  var bit = d.tag & 1;
	  d.tag >>>= 1;

	  return bit;
	}

	/* read a num bit value from a stream and add base */
	function tinf_read_bits(d, num, base) {
	  if (!num)
	    return base;

	  while (d.bitcount < 24) {
	    d.tag |= d.source[d.sourceIndex++] << d.bitcount;
	    d.bitcount += 8;
	  }

	  var val = d.tag & (0xffff >>> (16 - num));
	  d.tag >>>= num;
	  d.bitcount -= num;
	  return val + base;
	}

	/* given a data stream and a tree, decode a symbol */
	function tinf_decode_symbol(d, t) {
	  while (d.bitcount < 24) {
	    d.tag |= d.source[d.sourceIndex++] << d.bitcount;
	    d.bitcount += 8;
	  }
	  
	  var sum = 0, cur = 0, len = 0;
	  var tag = d.tag;

	  /* get more bits while code value is above sum */
	  do {
	    cur = 2 * cur + (tag & 1);
	    tag >>>= 1;
	    ++len;

	    sum += t.table[len];
	    cur -= t.table[len];
	  } while (cur >= 0);
	  
	  d.tag = tag;
	  d.bitcount -= len;

	  return t.trans[sum + cur];
	}

	/* given a data stream, decode dynamic trees from it */
	function tinf_decode_trees(d, lt, dt) {
	  var hlit, hdist, hclen;
	  var i, num, length;

	  /* get 5 bits HLIT (257-286) */
	  hlit = tinf_read_bits(d, 5, 257);

	  /* get 5 bits HDIST (1-32) */
	  hdist = tinf_read_bits(d, 5, 1);

	  /* get 4 bits HCLEN (4-19) */
	  hclen = tinf_read_bits(d, 4, 4);

	  for (i = 0; i < 19; ++i) lengths[i] = 0;

	  /* read code lengths for code length alphabet */
	  for (i = 0; i < hclen; ++i) {
	    /* get 3 bits code length (0-7) */
	    var clen = tinf_read_bits(d, 3, 0);
	    lengths[clcidx[i]] = clen;
	  }

	  /* build code length tree */
	  tinf_build_tree(code_tree, lengths, 0, 19);

	  /* decode code lengths for the dynamic trees */
	  for (num = 0; num < hlit + hdist;) {
	    var sym = tinf_decode_symbol(d, code_tree);

	    switch (sym) {
	      case 16:
	        /* copy previous code length 3-6 times (read 2 bits) */
	        var prev = lengths[num - 1];
	        for (length = tinf_read_bits(d, 2, 3); length; --length) {
	          lengths[num++] = prev;
	        }
	        break;
	      case 17:
	        /* repeat code length 0 for 3-10 times (read 3 bits) */
	        for (length = tinf_read_bits(d, 3, 3); length; --length) {
	          lengths[num++] = 0;
	        }
	        break;
	      case 18:
	        /* repeat code length 0 for 11-138 times (read 7 bits) */
	        for (length = tinf_read_bits(d, 7, 11); length; --length) {
	          lengths[num++] = 0;
	        }
	        break;
	      default:
	        /* values 0-15 represent the actual code lengths */
	        lengths[num++] = sym;
	        break;
	    }
	  }

	  /* build dynamic trees */
	  tinf_build_tree(lt, lengths, 0, hlit);
	  tinf_build_tree(dt, lengths, hlit, hdist);
	}

	/* ----------------------------- *
	 * -- block inflate functions -- *
	 * ----------------------------- */

	/* given a stream and two trees, inflate a block of data */
	function tinf_inflate_block_data(d, lt, dt) {
	  while (1) {
	    var sym = tinf_decode_symbol(d, lt);

	    /* check for end of block */
	    if (sym === 256) {
	      return TINF_OK;
	    }

	    if (sym < 256) {
	      d.dest[d.destLen++] = sym;
	    } else {
	      var length, dist, offs;
	      var i;

	      sym -= 257;

	      /* possibly get more bits from length code */
	      length = tinf_read_bits(d, length_bits[sym], length_base[sym]);

	      dist = tinf_decode_symbol(d, dt);

	      /* possibly get more bits from distance code */
	      offs = d.destLen - tinf_read_bits(d, dist_bits[dist], dist_base[dist]);

	      /* copy match */
	      for (i = offs; i < offs + length; ++i) {
	        d.dest[d.destLen++] = d.dest[i];
	      }
	    }
	  }
	}

	/* inflate an uncompressed block of data */
	function tinf_inflate_uncompressed_block(d) {
	  var length, invlength;
	  var i;
	  
	  /* unread from bitbuffer */
	  while (d.bitcount > 8) {
	    d.sourceIndex--;
	    d.bitcount -= 8;
	  }

	  /* get length */
	  length = d.source[d.sourceIndex + 1];
	  length = 256 * length + d.source[d.sourceIndex];

	  /* get one's complement of length */
	  invlength = d.source[d.sourceIndex + 3];
	  invlength = 256 * invlength + d.source[d.sourceIndex + 2];

	  /* check length */
	  if (length !== (~invlength & 0x0000ffff))
	    return TINF_DATA_ERROR;

	  d.sourceIndex += 4;

	  /* copy block */
	  for (i = length; i; --i)
	    d.dest[d.destLen++] = d.source[d.sourceIndex++];

	  /* make sure we start next block on a byte boundary */
	  d.bitcount = 0;

	  return TINF_OK;
	}

	/* inflate stream from source to dest */
	function tinf_uncompress(source, dest) {
	  var d = new Data(source, dest);
	  var bfinal, btype, res;

	  do {
	    /* read final block flag */
	    bfinal = tinf_getbit(d);

	    /* read block type (2 bits) */
	    btype = tinf_read_bits(d, 2, 0);

	    /* decompress block */
	    switch (btype) {
	      case 0:
	        /* decompress uncompressed block */
	        res = tinf_inflate_uncompressed_block(d);
	        break;
	      case 1:
	        /* decompress block with fixed huffman trees */
	        res = tinf_inflate_block_data(d, sltree, sdtree);
	        break;
	      case 2:
	        /* decompress block with dynamic huffman trees */
	        tinf_decode_trees(d, d.ltree, d.dtree);
	        res = tinf_inflate_block_data(d, d.ltree, d.dtree);
	        break;
	      default:
	        res = TINF_DATA_ERROR;
	    }

	    if (res !== TINF_OK)
	      throw new Error('Data error');

	  } while (!bfinal);

	  if (d.destLen < d.dest.length) {
	    if (typeof d.dest.slice === 'function')
	      return d.dest.slice(0, d.destLen);
	    else
	      return d.dest.subarray(0, d.destLen);
	  }
	  
	  return d.dest;
	}

	/* -------------------- *
	 * -- initialization -- *
	 * -------------------- */

	/* build fixed huffman trees */
	tinf_build_fixed_trees(sltree, sdtree);

	/* build extra bits and base tables */
	tinf_build_bits_base(length_bits, length_base, 4, 3);
	tinf_build_bits_base(dist_bits, dist_base, 2, 1);

	/* fix a special case */
	length_bits[28] = 0;
	length_base[28] = 258;

	tinyInflate = tinf_uncompress;
	return tinyInflate;
}

var swap_1;
var hasRequiredSwap;

function requireSwap () {
	if (hasRequiredSwap) return swap_1;
	hasRequiredSwap = 1;
	const isBigEndian = (new Uint8Array(new Uint32Array([0x12345678]).buffer)[0] === 0x12);

	const swap = (b, n, m) => {
	  let i = b[n];
	  b[n] = b[m];
	  b[m] = i;
	};

	const swap32 = array => {
	  const len = array.length;
	  for (let i = 0; i < len; i += 4) {
	    swap(array, i, i + 3);
	    swap(array, i + 1, i + 2);
	  }
	};

	const swap32LE = array => {
	  if (isBigEndian) {
	    swap32(array);
	  }
	};

	swap_1 = {
	  swap32LE: swap32LE
	};
	return swap_1;
}

var unicodeTrie;
var hasRequiredUnicodeTrie;

function requireUnicodeTrie () {
	if (hasRequiredUnicodeTrie) return unicodeTrie;
	hasRequiredUnicodeTrie = 1;
	const inflate = requireTinyInflate();
	const { swap32LE } = requireSwap();

	// Shift size for getting the index-1 table offset.
	const SHIFT_1 = 6 + 5;

	// Shift size for getting the index-2 table offset.
	const SHIFT_2 = 5;

	// Difference between the two shift sizes,
	// for getting an index-1 offset from an index-2 offset. 6=11-5
	const SHIFT_1_2 = SHIFT_1 - SHIFT_2;

	// Number of index-1 entries for the BMP. 32=0x20
	// This part of the index-1 table is omitted from the serialized form.
	const OMITTED_BMP_INDEX_1_LENGTH = 0x10000 >> SHIFT_1;

	// Number of entries in an index-2 block. 64=0x40
	const INDEX_2_BLOCK_LENGTH = 1 << SHIFT_1_2;

	// Mask for getting the lower bits for the in-index-2-block offset. */
	const INDEX_2_MASK = INDEX_2_BLOCK_LENGTH - 1;

	// Shift size for shifting left the index array values.
	// Increases possible data size with 16-bit index values at the cost
	// of compactability.
	// This requires data blocks to be aligned by DATA_GRANULARITY.
	const INDEX_SHIFT = 2;

	// Number of entries in a data block. 32=0x20
	const DATA_BLOCK_LENGTH = 1 << SHIFT_2;

	// Mask for getting the lower bits for the in-data-block offset.
	const DATA_MASK = DATA_BLOCK_LENGTH - 1;

	// The part of the index-2 table for U+D800..U+DBFF stores values for
	// lead surrogate code _units_ not code _points_.
	// Values for lead surrogate code _points_ are indexed with this portion of the table.
	// Length=32=0x20=0x400>>SHIFT_2. (There are 1024=0x400 lead surrogates.)
	const LSCP_INDEX_2_OFFSET = 0x10000 >> SHIFT_2;
	const LSCP_INDEX_2_LENGTH = 0x400 >> SHIFT_2;

	// Count the lengths of both BMP pieces. 2080=0x820
	const INDEX_2_BMP_LENGTH = LSCP_INDEX_2_OFFSET + LSCP_INDEX_2_LENGTH;

	// The 2-byte UTF-8 version of the index-2 table follows at offset 2080=0x820.
	// Length 32=0x20 for lead bytes C0..DF, regardless of SHIFT_2.
	const UTF8_2B_INDEX_2_OFFSET = INDEX_2_BMP_LENGTH;
	const UTF8_2B_INDEX_2_LENGTH = 0x800 >> 6;  // U+0800 is the first code point after 2-byte UTF-8

	// The index-1 table, only used for supplementary code points, at offset 2112=0x840.
	// Variable length, for code points up to highStart, where the last single-value range starts.
	// Maximum length 512=0x200=0x100000>>SHIFT_1.
	// (For 0x100000 supplementary code points U+10000..U+10ffff.)
	//
	// The part of the index-2 table for supplementary code points starts
	// after this index-1 table.
	//
	// Both the index-1 table and the following part of the index-2 table
	// are omitted completely if there is only BMP data.
	const INDEX_1_OFFSET = UTF8_2B_INDEX_2_OFFSET + UTF8_2B_INDEX_2_LENGTH;

	// The alignment size of a data block. Also the granularity for compaction.
	const DATA_GRANULARITY = 1 << INDEX_SHIFT;

	class UnicodeTrie {
	  constructor(data) {
	    const isBuffer = (typeof data.readUInt32BE === 'function') && (typeof data.slice === 'function');

	    if (isBuffer || data instanceof Uint8Array) {
	      // read binary format
	      let uncompressedLength;
	      if (isBuffer) {
	        this.highStart = data.readUInt32LE(0);
	        this.errorValue = data.readUInt32LE(4);
	        uncompressedLength = data.readUInt32LE(8);
	        data = data.slice(12);
	      } else {
	        const view = new DataView(data.buffer);
	        this.highStart = view.getUint32(0, true);
	        this.errorValue = view.getUint32(4, true);
	        uncompressedLength = view.getUint32(8, true);
	        data = data.subarray(12);
	      }

	      // double inflate the actual trie data
	      data = inflate(data, new Uint8Array(uncompressedLength));
	      data = inflate(data, new Uint8Array(uncompressedLength));

	      // swap bytes from little-endian
	      swap32LE(data);

	      this.data = new Uint32Array(data.buffer);

	    } else {
	      // pre-parsed data
	      ({ data: this.data, highStart: this.highStart, errorValue: this.errorValue } = data);
	    }
	  }

	  get(codePoint) {
	    let index;
	    if ((codePoint < 0) || (codePoint > 0x10ffff)) {
	      return this.errorValue;
	    }

	    if ((codePoint < 0xd800) || ((codePoint > 0xdbff) && (codePoint <= 0xffff))) {
	      // Ordinary BMP code point, excluding leading surrogates.
	      // BMP uses a single level lookup.  BMP index starts at offset 0 in the index.
	      // data is stored in the index array itself.
	      index = (this.data[codePoint >> SHIFT_2] << INDEX_SHIFT) + (codePoint & DATA_MASK);
	      return this.data[index];
	    }

	    if (codePoint <= 0xffff) {
	      // Lead Surrogate Code Point.  A Separate index section is stored for
	      // lead surrogate code units and code points.
	      //   The main index has the code unit data.
	      //   For this function, we need the code point data.
	      index = (this.data[LSCP_INDEX_2_OFFSET + ((codePoint - 0xd800) >> SHIFT_2)] << INDEX_SHIFT) + (codePoint & DATA_MASK);
	      return this.data[index];
	    }

	    if (codePoint < this.highStart) {
	      // Supplemental code point, use two-level lookup.
	      index = this.data[(INDEX_1_OFFSET - OMITTED_BMP_INDEX_1_LENGTH) + (codePoint >> SHIFT_1)];
	      index = this.data[index + ((codePoint >> SHIFT_2) & INDEX_2_MASK)];
	      index = (index << INDEX_SHIFT) + (codePoint & DATA_MASK);
	      return this.data[index];
	    }

	    return this.data[this.data.length - DATA_GRANULARITY];
	  }
	}

	unicodeTrie = UnicodeTrie;
	return unicodeTrie;
}

var base64$1 = {exports: {}};

var base64 = base64$1.exports;

var hasRequiredBase64;

function requireBase64 () {
	if (hasRequiredBase64) return base64$1.exports;
	hasRequiredBase64 = 1;
	(function (module, exports) {
		(function (global, factory) {
		    module.exports = factory()
		        ;
		}((typeof self !== 'undefined' ? self
		    : typeof window !== 'undefined' ? window
		        : typeof commonjsGlobal !== 'undefined' ? commonjsGlobal
		            : base64), function () {
		    /**
		     *  base64.ts
		     *
		     *  Licensed under the BSD 3-Clause License.
		     *    http://opensource.org/licenses/BSD-3-Clause
		     *
		     *  References:
		     *    http://en.wikipedia.org/wiki/Base64
		     *
		     * @author Dan Kogai (https://github.com/dankogai)
		     */
		    var version = '3.7.7';
		    /**
		     * @deprecated use lowercase `version`.
		     */
		    var VERSION = version;
		    var _hasBuffer = typeof Buffer === 'function';
		    var _TD = typeof TextDecoder === 'function' ? new TextDecoder() : undefined;
		    var _TE = typeof TextEncoder === 'function' ? new TextEncoder() : undefined;
		    var b64ch = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
		    var b64chs = Array.prototype.slice.call(b64ch);
		    var b64tab = (function (a) {
		        var tab = {};
		        a.forEach(function (c, i) { return tab[c] = i; });
		        return tab;
		    })(b64chs);
		    var b64re = /^(?:[A-Za-z\d+\/]{4})*?(?:[A-Za-z\d+\/]{2}(?:==)?|[A-Za-z\d+\/]{3}=?)?$/;
		    var _fromCC = String.fromCharCode.bind(String);
		    var _U8Afrom = typeof Uint8Array.from === 'function'
		        ? Uint8Array.from.bind(Uint8Array)
		        : function (it) { return new Uint8Array(Array.prototype.slice.call(it, 0)); };
		    var _mkUriSafe = function (src) { return src
		        .replace(/=/g, '').replace(/[+\/]/g, function (m0) { return m0 == '+' ? '-' : '_'; }); };
		    var _tidyB64 = function (s) { return s.replace(/[^A-Za-z0-9\+\/]/g, ''); };
		    /**
		     * polyfill version of `btoa`
		     */
		    var btoaPolyfill = function (bin) {
		        // console.log('polyfilled');
		        var u32, c0, c1, c2, asc = '';
		        var pad = bin.length % 3;
		        for (var i = 0; i < bin.length;) {
		            if ((c0 = bin.charCodeAt(i++)) > 255 ||
		                (c1 = bin.charCodeAt(i++)) > 255 ||
		                (c2 = bin.charCodeAt(i++)) > 255)
		                throw new TypeError('invalid character found');
		            u32 = (c0 << 16) | (c1 << 8) | c2;
		            asc += b64chs[u32 >> 18 & 63]
		                + b64chs[u32 >> 12 & 63]
		                + b64chs[u32 >> 6 & 63]
		                + b64chs[u32 & 63];
		        }
		        return pad ? asc.slice(0, pad - 3) + "===".substring(pad) : asc;
		    };
		    /**
		     * does what `window.btoa` of web browsers do.
		     * @param {String} bin binary string
		     * @returns {string} Base64-encoded string
		     */
		    var _btoa = typeof btoa === 'function' ? function (bin) { return btoa(bin); }
		        : _hasBuffer ? function (bin) { return Buffer.from(bin, 'binary').toString('base64'); }
		            : btoaPolyfill;
		    var _fromUint8Array = _hasBuffer
		        ? function (u8a) { return Buffer.from(u8a).toString('base64'); }
		        : function (u8a) {
		            // cf. https://stackoverflow.com/questions/12710001/how-to-convert-uint8-array-to-base64-encoded-string/12713326#12713326
		            var maxargs = 0x1000;
		            var strs = [];
		            for (var i = 0, l = u8a.length; i < l; i += maxargs) {
		                strs.push(_fromCC.apply(null, u8a.subarray(i, i + maxargs)));
		            }
		            return _btoa(strs.join(''));
		        };
		    /**
		     * converts a Uint8Array to a Base64 string.
		     * @param {boolean} [urlsafe] URL-and-filename-safe a la RFC4648 §5
		     * @returns {string} Base64 string
		     */
		    var fromUint8Array = function (u8a, urlsafe) {
		        if (urlsafe === void 0) { urlsafe = false; }
		        return urlsafe ? _mkUriSafe(_fromUint8Array(u8a)) : _fromUint8Array(u8a);
		    };
		    // This trick is found broken https://github.com/dankogai/js-base64/issues/130
		    // const utob = (src: string) => unescape(encodeURIComponent(src));
		    // reverting good old fationed regexp
		    var cb_utob = function (c) {
		        if (c.length < 2) {
		            var cc = c.charCodeAt(0);
		            return cc < 0x80 ? c
		                : cc < 0x800 ? (_fromCC(0xc0 | (cc >>> 6))
		                    + _fromCC(0x80 | (cc & 0x3f)))
		                    : (_fromCC(0xe0 | ((cc >>> 12) & 0x0f))
		                        + _fromCC(0x80 | ((cc >>> 6) & 0x3f))
		                        + _fromCC(0x80 | (cc & 0x3f)));
		        }
		        else {
		            var cc = 0x10000
		                + (c.charCodeAt(0) - 0xD800) * 0x400
		                + (c.charCodeAt(1) - 0xDC00);
		            return (_fromCC(0xf0 | ((cc >>> 18) & 0x07))
		                + _fromCC(0x80 | ((cc >>> 12) & 0x3f))
		                + _fromCC(0x80 | ((cc >>> 6) & 0x3f))
		                + _fromCC(0x80 | (cc & 0x3f)));
		        }
		    };
		    var re_utob = /[\uD800-\uDBFF][\uDC00-\uDFFFF]|[^\x00-\x7F]/g;
		    /**
		     * @deprecated should have been internal use only.
		     * @param {string} src UTF-8 string
		     * @returns {string} UTF-16 string
		     */
		    var utob = function (u) { return u.replace(re_utob, cb_utob); };
		    //
		    var _encode = _hasBuffer
		        ? function (s) { return Buffer.from(s, 'utf8').toString('base64'); }
		        : _TE
		            ? function (s) { return _fromUint8Array(_TE.encode(s)); }
		            : function (s) { return _btoa(utob(s)); };
		    /**
		     * converts a UTF-8-encoded string to a Base64 string.
		     * @param {boolean} [urlsafe] if `true` make the result URL-safe
		     * @returns {string} Base64 string
		     */
		    var encode = function (src, urlsafe) {
		        if (urlsafe === void 0) { urlsafe = false; }
		        return urlsafe
		            ? _mkUriSafe(_encode(src))
		            : _encode(src);
		    };
		    /**
		     * converts a UTF-8-encoded string to URL-safe Base64 RFC4648 §5.
		     * @returns {string} Base64 string
		     */
		    var encodeURI = function (src) { return encode(src, true); };
		    // This trick is found broken https://github.com/dankogai/js-base64/issues/130
		    // const btou = (src: string) => decodeURIComponent(escape(src));
		    // reverting good old fationed regexp
		    var re_btou = /[\xC0-\xDF][\x80-\xBF]|[\xE0-\xEF][\x80-\xBF]{2}|[\xF0-\xF7][\x80-\xBF]{3}/g;
		    var cb_btou = function (cccc) {
		        switch (cccc.length) {
		            case 4:
		                var cp = ((0x07 & cccc.charCodeAt(0)) << 18)
		                    | ((0x3f & cccc.charCodeAt(1)) << 12)
		                    | ((0x3f & cccc.charCodeAt(2)) << 6)
		                    | (0x3f & cccc.charCodeAt(3)), offset = cp - 0x10000;
		                return (_fromCC((offset >>> 10) + 0xD800)
		                    + _fromCC((offset & 0x3FF) + 0xDC00));
		            case 3:
		                return _fromCC(((0x0f & cccc.charCodeAt(0)) << 12)
		                    | ((0x3f & cccc.charCodeAt(1)) << 6)
		                    | (0x3f & cccc.charCodeAt(2)));
		            default:
		                return _fromCC(((0x1f & cccc.charCodeAt(0)) << 6)
		                    | (0x3f & cccc.charCodeAt(1)));
		        }
		    };
		    /**
		     * @deprecated should have been internal use only.
		     * @param {string} src UTF-16 string
		     * @returns {string} UTF-8 string
		     */
		    var btou = function (b) { return b.replace(re_btou, cb_btou); };
		    /**
		     * polyfill version of `atob`
		     */
		    var atobPolyfill = function (asc) {
		        // console.log('polyfilled');
		        asc = asc.replace(/\s+/g, '');
		        if (!b64re.test(asc))
		            throw new TypeError('malformed base64.');
		        asc += '=='.slice(2 - (asc.length & 3));
		        var u24, bin = '', r1, r2;
		        for (var i = 0; i < asc.length;) {
		            u24 = b64tab[asc.charAt(i++)] << 18
		                | b64tab[asc.charAt(i++)] << 12
		                | (r1 = b64tab[asc.charAt(i++)]) << 6
		                | (r2 = b64tab[asc.charAt(i++)]);
		            bin += r1 === 64 ? _fromCC(u24 >> 16 & 255)
		                : r2 === 64 ? _fromCC(u24 >> 16 & 255, u24 >> 8 & 255)
		                    : _fromCC(u24 >> 16 & 255, u24 >> 8 & 255, u24 & 255);
		        }
		        return bin;
		    };
		    /**
		     * does what `window.atob` of web browsers do.
		     * @param {String} asc Base64-encoded string
		     * @returns {string} binary string
		     */
		    var _atob = typeof atob === 'function' ? function (asc) { return atob(_tidyB64(asc)); }
		        : _hasBuffer ? function (asc) { return Buffer.from(asc, 'base64').toString('binary'); }
		            : atobPolyfill;
		    //
		    var _toUint8Array = _hasBuffer
		        ? function (a) { return _U8Afrom(Buffer.from(a, 'base64')); }
		        : function (a) { return _U8Afrom(_atob(a).split('').map(function (c) { return c.charCodeAt(0); })); };
		    /**
		     * converts a Base64 string to a Uint8Array.
		     */
		    var toUint8Array = function (a) { return _toUint8Array(_unURI(a)); };
		    //
		    var _decode = _hasBuffer
		        ? function (a) { return Buffer.from(a, 'base64').toString('utf8'); }
		        : _TD
		            ? function (a) { return _TD.decode(_toUint8Array(a)); }
		            : function (a) { return btou(_atob(a)); };
		    var _unURI = function (a) { return _tidyB64(a.replace(/[-_]/g, function (m0) { return m0 == '-' ? '+' : '/'; })); };
		    /**
		     * converts a Base64 string to a UTF-8 string.
		     * @param {String} src Base64 string.  Both normal and URL-safe are supported
		     * @returns {string} UTF-8 string
		     */
		    var decode = function (src) { return _decode(_unURI(src)); };
		    /**
		     * check if a value is a valid Base64 string
		     * @param {String} src a value to check
		      */
		    var isValid = function (src) {
		        if (typeof src !== 'string')
		            return false;
		        var s = src.replace(/\s+/g, '').replace(/={0,2}$/, '');
		        return !/[^\s0-9a-zA-Z\+/]/.test(s) || !/[^\s0-9a-zA-Z\-_]/.test(s);
		    };
		    //
		    var _noEnum = function (v) {
		        return {
		            value: v, enumerable: false, writable: true, configurable: true
		        };
		    };
		    /**
		     * extend String.prototype with relevant methods
		     */
		    var extendString = function () {
		        var _add = function (name, body) { return Object.defineProperty(String.prototype, name, _noEnum(body)); };
		        _add('fromBase64', function () { return decode(this); });
		        _add('toBase64', function (urlsafe) { return encode(this, urlsafe); });
		        _add('toBase64URI', function () { return encode(this, true); });
		        _add('toBase64URL', function () { return encode(this, true); });
		        _add('toUint8Array', function () { return toUint8Array(this); });
		    };
		    /**
		     * extend Uint8Array.prototype with relevant methods
		     */
		    var extendUint8Array = function () {
		        var _add = function (name, body) { return Object.defineProperty(Uint8Array.prototype, name, _noEnum(body)); };
		        _add('toBase64', function (urlsafe) { return fromUint8Array(this, urlsafe); });
		        _add('toBase64URI', function () { return fromUint8Array(this, true); });
		        _add('toBase64URL', function () { return fromUint8Array(this, true); });
		    };
		    /**
		     * extend Builtin prototypes with relevant methods
		     */
		    var extendBuiltins = function () {
		        extendString();
		        extendUint8Array();
		    };
		    var gBase64 = {
		        version: version,
		        VERSION: VERSION,
		        atob: _atob,
		        atobPolyfill: atobPolyfill,
		        btoa: _btoa,
		        btoaPolyfill: btoaPolyfill,
		        fromBase64: decode,
		        toBase64: encode,
		        encode: encode,
		        encodeURI: encodeURI,
		        encodeURL: encodeURI,
		        utob: utob,
		        btou: btou,
		        decode: decode,
		        isValid: isValid,
		        fromUint8Array: fromUint8Array,
		        toUint8Array: toUint8Array,
		        extendString: extendString,
		        extendUint8Array: extendUint8Array,
		        extendBuiltins: extendBuiltins
		    };
		    //
		    // export Base64 to the namespace
		    //
		    // ES5 is yet to have Object.assign() that may make transpilers unhappy.
		    // gBase64.Base64 = Object.assign({}, gBase64);
		    gBase64.Base64 = {};
		    Object.keys(gBase64).forEach(function (k) { return gBase64.Base64[k] = gBase64[k]; });
		    return gBase64;
		})); 
	} (base64$1, base64$1.exports));
	return base64$1.exports;
}

var graphemesplit;
var hasRequiredGraphemesplit;

function requireGraphemesplit () {
	if (hasRequiredGraphemesplit) return graphemesplit;
	hasRequiredGraphemesplit = 1;
	const types = requireTypes$2();
	const typeTrieData = require$$1.data;
	const extPictData = require$$2.data;
	const inCBData = require$$3.data;

	const UnicodeTrie = requireUnicodeTrie();
	const Base64 =  requireBase64().Base64;

	const typeTrie = new UnicodeTrie(Base64.toUint8Array(typeTrieData));
	const extPict = new UnicodeTrie(Base64.toUint8Array(extPictData));
	const inCB = new UnicodeTrie(Base64.toUint8Array(inCBData));

	function is(type, bit) {
	  return (type & bit) !== 0;
	}

	function nextGraphemeClusterSize(s, ts, start) {
	  const L = ts.length;

	  for (let i = start; i + 1 < L; i++) {
	    const curr = ts[i + 0];
	    const next = ts[i + 1];

	    // GB9c: \p{InCB=Consonant} [ \p{InCB=Extend} \p{InCB=Linker} ]* \p{InCB=Linker} [ \p{InCB=Extend} \p{InCB=Linker} ]* × \p{InCB=Consonant}
	    switch (s.gb9c) {
	    case 0:
	      if (is(curr, types.InCB_Consonant)) s.gb9c = 1;
	      break;
	    case 1:
	      if (is(curr, types.InCB_Extend)) s.gb9c = 1;
	      else if (is(curr, types.InCB_Linker)) s.gb9c = 2;
	      else s.gb9c = is(curr, types.InCB_Consonant) ? 1 : 0;
	      break;
	    case 2:
	      if (is(curr, types.InCB_Extend | types.InCB_Linker)) s.gb9c = 2;
	      else s.gb9c = is(curr, types.InCB_Consonant) ? 1 : 0;
	      break;
	    }

	    // GB11: \p{Extended_Pictographic} Extend* ZWJ x \p{Extended_Pictographic}
	    switch (s.gb11) {
	    case 0:
	      if (is(curr, types.Extended_Pictographic)) s.gb11 = 1;
	      break;
	    case 1:
	      if (is(curr, types.Extend)) s.gb11 = 1;
	      else if (is(curr, types.ZWJ)) s.gb11 = 2;
	      else s.gb11 = is(curr, types.Extended_Pictographic) ? 1 : 0;
	      break;
	    case 2:
	      s.gb11 = is(curr, types.Extended_Pictographic) ? 1 : 0;
	      break;
	    }

	    // GB12: sot (RI RI)* RI × RI
	    switch (s.gb12) {
	    case 0:
	      if (is(curr, types.Regional_Indicator)) s.gb12 = 1;
	      else s.gb12 = -1;
	      break;
	    case 1:
	      if (is(curr, types.Regional_Indicator)) s.gb12 = 0;
	      else s.gb12 = -1;
	      break;
	    }

	    // GB13: [^RI] (RI RI)* RI × RI
	    switch (s.gb13) {
	    case 0:
	      if (!is(curr, types.Regional_Indicator)) s.gb13 = 1;
	      break;
	    case 1:
	      if (is(curr, types.Regional_Indicator)) s.gb13 = 2;
	      else s.gb13 = 1;
	      break;
	    case 2:
	      s.gb13 = 1;
	      break;
	    }

	    // GB3: CR x LF
	    if (is(curr, types.CR) && is(next, types.LF)) {
	      continue;
	    }
	    // GB4: (Control | CR | LF) ÷
	    if (is(curr, types.Control | types.CR | types.LF)) {
	      return i + 1 - start;
	    }
	    // GB5: ÷ (Control | CR | LF)
	    if (is(next, types.Control | types.CR | types.LF)) {
	      return i + 1 - start;
	    }
	    // GB6: L x (L | V | LV | LVT)
	    if (
	      is(curr, types.L) &&
	      is(next, types.L | types.V | types.LV | types.LVT)
	    ) {
	      continue;
	    }
	    // GB7: (LV | V) x (V | T)
	    if (is(curr, types.LV | types.V) && is(next, types.V | types.T)) {
	      continue;
	    }
	    // GB8: (LVT | T) x T
	    if (is(curr, types.LVT | types.T) && is(next, types.T)) {
	      continue;
	    }
	    // GB9: x (Extend | ZWJ)
	    if (is(next, types.Extend | types.ZWJ)) {
	      continue;
	    }
	    // GB9a: x SpacingMark
	    if (is(next, types.SpacingMark)) {
	      continue;
	    }
	    // GB9b: Prepend x
	    if (is(curr, types.Prepend)) {
	      continue;
	    }
	    // GB9c: \p{InCB=Consonant} [ \p{InCB=Extend} \p{InCB=Linker} ]* \p{InCB=Linker} [ \p{InCB=Extend} \p{InCB=Linker} ]* × \p{InCB=Consonant}
	    if (is(next, types.InCB_Consonant) && s.gb9c === 2) {
	      continue;
	    }
	    // GB11: \p{Extended_Pictographic} Extend* ZWJ x \p{Extended_Pictographic}
	    if (is(next, types.Extended_Pictographic) && s.gb11 === 2) {
	      continue;
	    }
	    // GB12: sot (RI RI)* RI x RI
	    if (is(next, types.Regional_Indicator) && s.gb12 === 1) {
	      continue;
	    }
	    // GB13: [^RI] (RI RI)* RI x RI
	    if (is(next, types.Regional_Indicator) && s.gb13 === 2) {
	      continue;
	    }
	    // GB999: Any ÷ Any
	    return i + 1 - start;
	  }
	  return L - start;
	}

	graphemesplit = function split(str) {
	  const graphemeClusters = [];

	  const map = [0];
	  const ts = [];
	  for (let i = 0; i < str.length; ) {
	    const code = str.codePointAt(i);
	    ts.push(typeTrie.get(code) | extPict.get(code) | inCB.get(code));
	    i += code > 65535 ? 2 : 1;
	    map.push(i);
	  }
	  const s = {
	    gb9c: 0,
	    gb11: 0,
	    gb12: 0,
	    gb13: 0,
	  };
	  for (let offset = 0; offset < ts.length; ) {
	    const size = nextGraphemeClusterSize(s, ts, offset);
	    const start = map[offset];
	    const end = map[offset + size];
	    graphemeClusters.push(str.slice(start, end));
	    offset += size;
	  }

	  return graphemeClusters;
	};
	return graphemesplit;
}

var hasRequiredFuzzy;

function requireFuzzy () {
	if (hasRequiredFuzzy) return fuzzy;
	hasRequiredFuzzy = 1;

	Object.defineProperty(fuzzy, '__esModule', { value: true });

	function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

	var split = _interopDefault(requireGraphemesplit());

	const splitUnicode = str => str.normalize("NFKD").split("");

	const whitespaceRegex = /^\s+$/;
	const nonWordRegex = /^[`~!@#$%^&*()\-=_+{}[\]\|\\;':",./<>?]+$/;
	const sortKind = {
	  insertOrder: "insertOrder",
	  bestMatch: "bestMatch"
	}; // the default options, which will be used for any unset option

	const defaultOptions = {
	  keySelector: s => s,
	  threshold: .6,
	  ignoreCase: true,
	  ignoreSymbols: true,
	  normalizeWhitespace: true,
	  returnMatchData: false,
	  useDamerau: true,
	  useSellers: true,
	  useSeparatedUnicode: false,
	  sortBy: sortKind.bestMatch
	};

	const noop = () => {};

	const arrayWrap = item => item instanceof Array ? item : [item]; // return normalized string, with map included


	function normalize(string, options) {
	  const lower = options.ignoreCase ? string.toLocaleLowerCase() : string; // track transformations

	  const normal = [];
	  const map = [];
	  let lastWasWhitespace = true;
	  let length = 0;
	  const graphemeList = options.useSeparatedUnicode ? splitUnicode(lower) : split(lower);

	  for (const grapheme of graphemeList) {
	    whitespaceRegex.lastIndex = 0;
	    nonWordRegex.lastIndex = 0;

	    if (options.normalizeWhitespace && whitespaceRegex.test(grapheme)) {
	      if (!lastWasWhitespace) {
	        normal.push(" ");
	        map.push(length);
	        lastWasWhitespace = true;
	      }
	    } else if (!(options.ignoreSymbols && nonWordRegex.test(grapheme))) {
	      if (options.useSeparatedUnicode) {
	        normal.push(grapheme);
	      } else {
	        normal.push(grapheme.normalize());
	      }

	      map.push(length);
	      lastWasWhitespace = false;
	    }

	    length += grapheme.length;
	  } // add the end of the string


	  map.push(string.length);

	  while (normal[normal.length - 1] === " ") {
	    normal.pop();
	    map.pop();
	  }

	  return {
	    original: string,
	    normal,
	    map
	  };
	} // translates a match to the original string


	function denormalizeMatchPosition(match, map) {
	  return {
	    index: map[match.start],
	    length: map[match.end + 1] - map[match.start]
	  };
	} // walks back up the matrix to find the match index and length


	function walkBack(rows, scoreIndex) {
	  if (scoreIndex === 0) {
	    return {
	      index: 0,
	      length: 0
	    };
	  }

	  let start = scoreIndex;

	  for (let i = rows.length - 2; i > 0 && start > 1; i--) {
	    const row = rows[i];
	    start = row[start] < row[start - 1] ? start : start - 1;
	  }

	  return {
	    start: start - 1,
	    end: scoreIndex - 1
	  };
	} // walkback is a noop for non-sellers, but should still return an object


	function noopWalkback() {
	  return {
	    start: 0,
	    end: 0
	  };
	}

	const levUpdateScore = () => true;

	const sellersUpdateScore = (cur, min) => cur < min;

	function getLevScore(rows, length) {
	  const lastRow = rows[rows.length - 1];
	  const lastCell = lastRow[length - 1];
	  const scoreLength = Math.max(rows.length, length);
	  return {
	    score: 1 - lastCell / (scoreLength - 1),
	    scoreIndex: length - 1
	  };
	}

	function getSellersScore(rows, length) {
	  // search term was empty string, return perfect score
	  if (rows.length === 1) {
	    return {
	      score: 1,
	      scoreIndex: 0
	    };
	  }

	  const lastRow = rows[rows.length - 1];
	  let minValue = lastRow[0];
	  let minIndex = 0;

	  for (let i = 1; i < length; i++) {
	    const val = lastRow[i];

	    if (val < minValue) {
	      minValue = val;
	      minIndex = i;
	    }
	  }

	  return {
	    score: 1 - minValue / (rows.length - 1),
	    scoreIndex: minIndex
	  };
	}

	function initLevRows(rowCount, columnCount) {
	  const rows = new Array(rowCount);

	  for (let i = 0; i < rowCount; i++) {
	    rows[i] = new Array(columnCount);
	    rows[i][0] = i;
	  }

	  for (let i = 0; i < columnCount; i++) {
	    rows[0][i] = i;
	  }

	  return rows;
	}

	function initSellersRows(rowCount, columnCount) {
	  const rows = new Array(rowCount);
	  rows[0] = new Array(columnCount).fill(0);

	  for (let i = 1; i < rowCount; i++) {
	    rows[i] = new Array(columnCount);
	    rows[i][0] = i;
	  }

	  return rows;
	} // the content of the innermost loop of levenshtein


	function levCore(term, candidate, rows, i, j) {
	  const rowA = rows[i];
	  const rowB = rows[i + 1];
	  const cost = term[i] === candidate[j] ? 0 : 1;
	  let m;
	  let min = rowB[j] + 1; // insertion

	  if ((m = rowA[j + 1] + 1) < min) min = m; // deletion

	  if ((m = rowA[j] + cost) < min) min = m; // substitution

	  rowB[j + 1] = min;
	} // runtime complexity: O(mn) where m and n are the lengths of term and candidate, respectively
	// Note: this method only runs on a single column


	function levenshtein(term, candidate, rows, j) {
	  for (let i = 0; i < term.length; i++) {
	    levCore(term, candidate, rows, i, j);
	  }
	} // has all the runtime characteristics of the above, but punishes transpositions less,
	// resulting in better tolerance to those types of typos
	// Note: this method only runs on a single column


	function damerauLevenshtein(term, candidate, rows, j) {
	  // if j === 0, we can't check for transpositions,
	  // so use normal levenshtein instead
	  if (j === 0) {
	    levenshtein(term, candidate, rows, j);
	    return;
	  } // for i === 0, we also can't check for transpositions, so calculate
	  // the first row using normal levenshtein as well


	  if (term.length > 0) {
	    levCore(term, candidate, rows, 0, j);
	  }

	  for (let i = 1; i < term.length; i++) {
	    const rowA = rows[i - 1];
	    const rowB = rows[i];
	    const rowC = rows[i + 1];
	    const cost = term[i] === candidate[j] ? 0 : 1;
	    let m; // insertion

	    let min = rowC[j] + 1; // deletion

	    if ((m = rowB[j + 1] + 1) < min) min = m; // substitution

	    if ((m = rowB[j] + cost) < min) min = m; // transposition

	    if (term[i] === candidate[j - 1] && term[i - 1] === candidate[j] && (m = rowA[j - 1] + cost) < min) min = m;
	    rowC[j + 1] = min;
	  }
	} // method for creating a trie from search candidates
	// using a trie can significantly improve search time


	function trieInsert(trie, string, item) {
	  let walker = trie;

	  for (let i = 0; i < string.length; i++) {
	    const char = string[i]; // add child node if not already present

	    if (walker.children[char] == null) {
	      walker.children[char] = {
	        children: {},
	        candidates: [],
	        depth: 0
	      };
	    } // log max depth of this subtree


	    walker.depth = Math.max(walker.depth, string.length - i); // step into child node

	    walker = walker.children[char];
	  }

	  walker.candidates.push(item);
	} // transforms a list of candidates into objects with normalized search keys,
	// and inserts them into a trie
	// the keySelector is used to pick strings from an object to search by


	function createSearchTrie(trie, index, items, options) {
	  for (const item of items) {
	    const candidates = arrayWrap(options.keySelector(item)).map((key, keyIndex) => ({
	      index,
	      keyIndex,
	      item,
	      normalized: normalize(key, options)
	    }));
	    index++;

	    for (const candidate of candidates) {
	      trieInsert(trie, candidate.normalized.normal, candidate);
	    }
	  }
	} // scored item comparator


	function compareItemsBestScore(a, b) {
	  // highest priority is raw levenshtein score
	  const scoreDiff = b.score - a.score;

	  if (scoreDiff !== 0) {
	    return scoreDiff;
	  } // ties are broken by earlier match positions


	  const matchPosDiff = a.match.start - b.match.start;

	  if (matchPosDiff !== 0) {
	    return matchPosDiff;
	  } // prioritize earlier keys


	  const keyIndexDiff = a.keyIndex - b.keyIndex;

	  if (keyIndexDiff !== 0) {
	    return keyIndexDiff;
	  } // lastly, break ties by preferring the closer length match


	  const lengthDiff = a.lengthDiff - b.lengthDiff;

	  if (lengthDiff !== 0) {
	    return lengthDiff;
	  } // if all else fails, resort to insertion order


	  return compareItemsInsertOrder(a, b);
	}

	function compareItemsInsertOrder(a, b) {
	  return a.index - b.index;
	}

	function getCompareFunc(sortBy) {
	  switch (sortBy) {
	    case sortKind.bestMatch:
	      return compareItemsBestScore;

	    case sortKind.insertOrder:
	      return compareItemsInsertOrder;

	    default:
	      throw new Error(`unknown sortBy method ${sortBy}`);
	  }
	} // dedupes and adds results to the results list/map


	function addResult(results, resultMap, candidate, score, match, lengthDiff, compareItems) {
	  const scoredItem = {
	    item: candidate.item,
	    normalized: candidate.normalized,
	    score,
	    match,
	    index: candidate.index,
	    keyIndex: candidate.keyIndex,
	    lengthDiff
	  };

	  if (resultMap[candidate.index] == null) {
	    resultMap[candidate.index] = results.length;
	    results.push(scoredItem);
	  } else if (compareItems(scoredItem, results[resultMap[candidate.index]]) < 0) {
	    results[resultMap[candidate.index]] = scoredItem;
	  }
	}

	const getLevLength = Math.max;

	const getSellersLength = termLength => termLength; // skip any subtrees for which it is impossible to score >= threshold


	function levShouldContinue(node, pos, term, threshold, sValue) {
	  // earliest point (length) at which sValue could return to 0
	  const p1 = pos + sValue; // point (length) at which string lengths would match

	  const p2 = Math.min(term.length, pos + node.depth + 1); // the best score possible is the string which minimizes the value
	  // max(sValue, strLenDiff), which is always halfway between p1 and p2

	  const length = Math.ceil((p1 + p2) / 2);
	  const bestPossibleValue = length - p2;
	  return 1 - bestPossibleValue / length >= threshold;
	}

	function sellersShouldContinue(node, _, term, threshold, sValue, lastValue) {
	  const bestPossibleValue = Math.min(sValue, lastValue - (node.depth + 1));
	  return 1 - bestPossibleValue / term.length >= threshold;
	} // (pseudo) recursively walk the trie


	function searchRecurse(trie, term, scoreMethods, rows, results, resultMap, options) {
	  const stack = [];

	  for (const key in trie.children) {
	    const node = trie.children[key];
	    stack.push([node, 1, key, 0, term.length]);
	  }

	  const acc = new Array(trie.depth);

	  while (stack.length !== 0) {
	    const [node, len, char, si, sv] = stack.pop();
	    acc[len - 1] = char; // build rows

	    scoreMethods.score(term, acc, rows, len - 1); // track best score and position

	    const lastIndex = len;
	    const lastValue = rows[rows.length - 1][lastIndex];
	    let sIndex = si,
	        sValue = sv;

	    if (scoreMethods.shouldUpdateScore(lastValue, sv)) {
	      sIndex = lastIndex;
	      sValue = lastValue;
	    } // insert results


	    if (node.candidates.length > 0) {
	      const length = scoreMethods.getLength(term.length, len);
	      const score = 1 - sValue / length;

	      if (score >= options.threshold) {
	        const match = walkBack(rows, sIndex);
	        const lengthDiff = Math.abs(len - term.length);

	        for (const candidate of node.candidates) {
	          addResult(results, resultMap, candidate, score, match, lengthDiff, scoreMethods.compareItems);
	        }
	      }
	    } // recurse for children


	    for (const key in node.children) {
	      const child = node.children[key];

	      if (scoreMethods.shouldContinue(child, len, term, options.threshold, sValue, lastValue)) {
	        stack.push([child, len + 1, key, sIndex, sValue]);
	      }
	    }
	  }
	} // the core match finder: returns a sorted, filtered list of matches
	// this does not normalize input, requiring users to normalize themselves


	function searchCore(term, trie, options) {
	  const initMethod = options.useSellers ? initSellersRows : initLevRows;
	  const scoreMethods = {
	    score: options.useDamerau ? damerauLevenshtein : levenshtein,
	    getLength: options.useSellers ? getSellersLength : getLevLength,
	    shouldUpdateScore: options.useSellers ? sellersUpdateScore : levUpdateScore,
	    shouldContinue: options.useSellers ? sellersShouldContinue : levShouldContinue,
	    walkBack: options.useSellers ? walkBack : noopWalkback,
	    compareItems: getCompareFunc(options.sortBy)
	  }; // walk the trie, scoring and storing the candidates

	  const resultMap = {};
	  const results = [];
	  const rows = initMethod(term.length + 1, trie.depth + 1);

	  if (options.threshold <= 0 || term.length === 0) {
	    for (const candidate of trie.candidates) {
	      addResult(results, resultMap, candidate, 0, {
	        index: 0,
	        length: 0
	      }, term.length, scoreMethods.compareItems);
	    }
	  }

	  searchRecurse(trie, term, scoreMethods, rows, results, resultMap, options);
	  const sorted = results.sort(scoreMethods.compareItems);

	  if (options.returnMatchData) {
	    const denormalize = options.useSellers ? denormalizeMatchPosition : noop;
	    return sorted.map(candidate => ({
	      item: candidate.item,
	      original: candidate.normalized.original,
	      key: candidate.normalized.normal.join(""),
	      score: candidate.score,
	      match: denormalize(candidate.match, candidate.normalized.map)
	    }));
	  }

	  return sorted.map(candidate => candidate.item);
	} // wrapper for exporting sellers while allowing options to be passed in


	function fuzzy$1(term, candidate, options) {
	  options = { ...defaultOptions,
	    ...options
	  };
	  const initMethod = options.useSellers ? initSellersRows : initLevRows;
	  const scoreMethod = options.useDamerau ? damerauLevenshtein : levenshtein;
	  const getScore = options.useSellers ? getSellersScore : getLevScore;
	  term = normalize(term, options).normal;
	  const normalized = normalize(candidate, options);
	  const rows = initMethod(term.length + 1, normalized.normal.length + 1);

	  for (let j = 0; j < normalized.normal.length; j++) {
	    scoreMethod(term, normalized.normal, rows, j);
	  }

	  const scoreResult = getScore(rows, normalized.normal.length + 1);
	  return options.returnMatchData ? {
	    item: candidate,
	    original: normalized.original,
	    key: normalized.normal.join(""),
	    score: scoreResult.score,
	    match: options.useSellers ? denormalizeMatchPosition(walkBack(rows, scoreResult.scoreIndex), normalized.map) : noop()
	  } : scoreResult.score;
	} // simple one-off search. Useful if you don't expect to use the same candidate list again

	function search(term, candidates, options) {
	  options = { ...defaultOptions,
	    ...options
	  };
	  const trie = {
	    children: {},
	    candidates: [],
	    depth: 0
	  };
	  createSearchTrie(trie, 0, candidates, options);
	  return searchCore(normalize(term, options).normal, trie, options);
	} // class that improves performance of searching the same set multiple times
	// normalizes the strings and caches the result for future calls

	class Searcher {
	  constructor(candidates, options) {
	    this.options = Object.assign({}, defaultOptions, options);
	    this.trie = {
	      children: {},
	      candidates: [],
	      depth: 0
	    };
	    createSearchTrie(this.trie, 0, candidates, this.options);
	    this.count = candidates.length;
	  }

	  add(...candidates) {
	    createSearchTrie(this.trie, this.count, candidates, this.options);
	    this.count += candidates.length;
	  }

	  search(term, options) {
	    options = Object.assign({}, this.options, options);
	    return searchCore(normalize(term, this.options).normal, this.trie, options);
	  }

	}

	fuzzy.Searcher = Searcher;
	fuzzy.fuzzy = fuzzy$1;
	fuzzy.search = search;
	fuzzy.sortKind = sortKind;
	return fuzzy;
}

var hasRequiredAutomapSignals;

function requireAutomapSignals () {
	if (hasRequiredAutomapSignals) return automapSignals;
	hasRequiredAutomapSignals = 1;
	Object.defineProperty(automapSignals, "__esModule", { value: true });
	automapSignals.isMappedSignalValue = isMappedSignalValue;
	automapSignals.autoMapSignals = autoMapSignals;
	const fast_fuzzy_1 = requireFuzzy();
	function isMappedSignalValue(mappedSignalId) {
	    if (typeof mappedSignalId === 'number') {
	        return true;
	    }
	    return typeof mappedSignalId === 'string' && mappedSignalId.length > 0;
	}
	function findMatchForDesignSignal(designSignal, packageSignals, sigmaModelSignals, sigmaModelToPackageSignalMap) {
	    for (const packageSignal of packageSignals) {
	        if (designSignal.name.toLowerCase() === packageSignal.name.toLowerCase()) {
	            return packageSignal.id;
	        }
	    }
	    for (const sigmaModelSignal of sigmaModelSignals) {
	        const sigmaSignalName = sigmaModelSignal.name || String(sigmaModelSignal.id);
	        if (designSignal.name.toLowerCase() === sigmaSignalName.toLowerCase()) {
	            const mappedPackageSignal = sigmaModelToPackageSignalMap.get(sigmaModelSignal.id);
	            if (mappedPackageSignal) {
	                return mappedPackageSignal.id;
	            }
	        }
	    }
	    for (const sigmaModelSignal of sigmaModelSignals) {
	        if (sigmaModelSignal.aliases) {
	            const aliasMatch = sigmaModelSignal.aliases.some((alias) => alias.toLowerCase() === designSignal.name.toLowerCase());
	            if (aliasMatch) {
	                const mappedPackageSignal = sigmaModelToPackageSignalMap.get(sigmaModelSignal.id);
	                if (mappedPackageSignal) {
	                    return mappedPackageSignal.id;
	                }
	            }
	        }
	    }
	    const packageSignalNames = packageSignals.map((signal) => signal.name);
	    const matches = (0, fast_fuzzy_1.search)(designSignal.name, packageSignalNames, {
	        returnMatchData: true,
	        threshold: 0.6,
	        ignoreCase: true,
	        ignoreSymbols: true,
	        normalizeWhitespace: true,
	    });
	    if (matches.length > 0) {
	        const bestMatch = matches[0];
	        if (bestMatch) {
	            const bestMatchName = bestMatch.item;
	            const bestMatchSignal = packageSignals.find((signal) => signal.name === bestMatchName);
	            if (bestMatchSignal) {
	                return bestMatchSignal.id;
	            }
	        }
	    }
	    return null;
	}
	function autoMapSignals(designSignals, packageSignals, sigmaModelType, baseComponentModelResolver) {
	    const signalMap = {};
	    let sigmaModelSignals = [];
	    if (sigmaModelType && baseComponentModelResolver) {
	        try {
	            const modelClass = baseComponentModelResolver.Models.find((m) => m.Model.type === sigmaModelType);
	            if (modelClass) {
	                sigmaModelSignals = modelClass.Model.signals || [];
	            }
	        }
	        catch {
	        }
	    }
	    const sigmaModelToPackageSignalMap = new Map();
	    for (const packageSignal of packageSignals) {
	        if (packageSignal.sigmaModelSignal) {
	            sigmaModelToPackageSignalMap.set(packageSignal.sigmaModelSignal, packageSignal);
	        }
	    }
	    const mappedSigmaModelSignals = sigmaModelSignals.filter((sigmaSignal) => {
	        return sigmaModelToPackageSignalMap.has(sigmaSignal.id) || sigmaModelToPackageSignalMap.has(sigmaSignal.name);
	    });
	    for (const designSignal of designSignals) {
	        const matchedPackageSignalId = findMatchForDesignSignal(designSignal, packageSignals, mappedSigmaModelSignals, sigmaModelToPackageSignalMap);
	        if (matchedPackageSignalId !== null) {
	            signalMap[designSignal.id] = matchedPackageSignalId;
	        }
	    }
	    return signalMap;
	}
	
	return automapSignals;
}

var hasRequiredSignalMapping;

function requireSignalMapping () {
	if (hasRequiredSignalMapping) return SignalMapping;
	hasRequiredSignalMapping = 1;
	(function (exports) {
		var __createBinding = (SignalMapping && SignalMapping.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (SignalMapping && SignalMapping.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAutomapSignals(), exports);
		
	} (SignalMapping));
	return SignalMapping;
}

var Events = {};

var packageEvents = {};

var event = {};

var hasRequiredEvent;

function requireEvent () {
	if (hasRequiredEvent) return event;
	hasRequiredEvent = 1;
	Object.defineProperty(event, "__esModule", { value: true });
	event.Event = void 0;
	event.createEventClass = createEventClass;
	const zod_1 = /*@__PURE__*/ requireZod();
	class Event {
	    static validateEmission(data) {
	        return this.emissionSchema.parse(data);
	    }
	    static getName() {
	        return this.eventName;
	    }
	    static getEmissionKeys() {
	        return this.emissionSchema instanceof zod_1.z.ZodObject ? Object.keys(this.emissionSchema.shape) : [];
	    }
	}
	event.Event = Event;
	function createEventClass(eventName, emissionSchema) {
	    const fullSchema = emissionSchema.extend({ timeGenerated: zod_1.z.date(), dataIsValid: zod_1.z.boolean().optional() });
	    return class extends Event {
	        static { this.eventName = eventName; }
	        static { this.schema = fullSchema; }
	        static { this.emissionSchema = emissionSchema; }
	    };
	}
	
	return event;
}

var buildAncestry = {};

var hasRequiredBuildAncestry;

function requireBuildAncestry () {
	if (hasRequiredBuildAncestry) return buildAncestry;
	hasRequiredBuildAncestry = 1;
	Object.defineProperty(buildAncestry, "__esModule", { value: true });
	buildAncestry.buildAncestryShape = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	buildAncestry.buildAncestryShape = {
	    buildId: zod_1.z.string().optional(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	};
	
	return buildAncestry;
}

var packageMilestoneEnums = {};

var hasRequiredPackageMilestoneEnums;

function requirePackageMilestoneEnums () {
	if (hasRequiredPackageMilestoneEnums) return packageMilestoneEnums;
	hasRequiredPackageMilestoneEnums = 1;
	Object.defineProperty(packageMilestoneEnums, "__esModule", { value: true });
	packageMilestoneEnums.PackageUnderstandingSkipCause = packageMilestoneEnums.PackageCreationOrigin = packageMilestoneEnums.PackageUnderstandingOutcome = packageMilestoneEnums.DatasheetContextKind = packageMilestoneEnums.DatasheetUrlDeadEnd = packageMilestoneEnums.DatasheetSearchSource = packageMilestoneEnums.DatasheetSearchOutcome = void 0;
	var DatasheetSearchOutcome;
	(function (DatasheetSearchOutcome) {
	    DatasheetSearchOutcome["Found"] = "found";
	    DatasheetSearchOutcome["NotFound"] = "not_found";
	    DatasheetSearchOutcome["Failed"] = "failed";
	})(DatasheetSearchOutcome || (packageMilestoneEnums.DatasheetSearchOutcome = DatasheetSearchOutcome = {}));
	var DatasheetSearchSource;
	(function (DatasheetSearchSource) {
	    DatasheetSearchSource["Url"] = "url";
	    DatasheetSearchSource["LinkedUrl"] = "linked_url";
	    DatasheetSearchSource["Agent"] = "agent";
	    DatasheetSearchSource["None"] = "none";
	})(DatasheetSearchSource || (packageMilestoneEnums.DatasheetSearchSource = DatasheetSearchSource = {}));
	var DatasheetUrlDeadEnd;
	(function (DatasheetUrlDeadEnd) {
	    DatasheetUrlDeadEnd["AccessDenied"] = "access_denied";
	    DatasheetUrlDeadEnd["ConnectionFailed"] = "connection_failed";
	    DatasheetUrlDeadEnd["NotFound"] = "not_found";
	    DatasheetUrlDeadEnd["NotADatasheet"] = "not_a_datasheet";
	    DatasheetUrlDeadEnd["ServerError"] = "server_error";
	})(DatasheetUrlDeadEnd || (packageMilestoneEnums.DatasheetUrlDeadEnd = DatasheetUrlDeadEnd = {}));
	var DatasheetContextKind;
	(function (DatasheetContextKind) {
	    DatasheetContextKind["Package"] = "package";
	    DatasheetContextKind["ProjectModule"] = "project_module";
	})(DatasheetContextKind || (packageMilestoneEnums.DatasheetContextKind = DatasheetContextKind = {}));
	var PackageUnderstandingOutcome;
	(function (PackageUnderstandingOutcome) {
	    PackageUnderstandingOutcome["Completed"] = "completed";
	    PackageUnderstandingOutcome["Autopublished"] = "autopublished";
	    PackageUnderstandingOutcome["CatalogueOnlyNoDatasheet"] = "catalogue_only_no_datasheet";
	    PackageUnderstandingOutcome["NotEligibleForAiExtraction"] = "not_eligible_for_ai_extraction";
	    PackageUnderstandingOutcome["NeedsSubModels"] = "needs_sub_models";
	})(PackageUnderstandingOutcome || (packageMilestoneEnums.PackageUnderstandingOutcome = PackageUnderstandingOutcome = {}));
	var PackageCreationOrigin;
	(function (PackageCreationOrigin) {
	    PackageCreationOrigin["Ingestion"] = "ingestion";
	    PackageCreationOrigin["Catalogue"] = "catalogue";
	    PackageCreationOrigin["Manual"] = "manual";
	    PackageCreationOrigin["Cli"] = "cli";
	    PackageCreationOrigin["CustomerBomMerge"] = "customer_bom_merge";
	    PackageCreationOrigin["Unknown"] = "unknown";
	})(PackageCreationOrigin || (packageMilestoneEnums.PackageCreationOrigin = PackageCreationOrigin = {}));
	var PackageUnderstandingSkipCause;
	(function (PackageUnderstandingSkipCause) {
	    PackageUnderstandingSkipCause["NoDatasheet"] = "no_datasheet";
	    PackageUnderstandingSkipCause["DispatchFailed"] = "dispatch_failed";
	    PackageUnderstandingSkipCause["NeverEvaluated"] = "never_evaluated";
	    PackageUnderstandingSkipCause["DetailsUnavailable"] = "details_unavailable";
	    PackageUnderstandingSkipCause["AlreadyRunning"] = "already_running";
	})(PackageUnderstandingSkipCause || (packageMilestoneEnums.PackageUnderstandingSkipCause = PackageUnderstandingSkipCause = {}));
	
	return packageMilestoneEnums;
}

var hasRequiredPackageEvents;

function requirePackageEvents () {
	if (hasRequiredPackageEvents) return packageEvents;
	hasRequiredPackageEvents = 1;
	Object.defineProperty(packageEvents, "__esModule", { value: true });
	packageEvents.PackageFamilyEditedEvent = packageEvents.PackageAutopublishSkippedEvent = packageEvents.PackageAutopublishedEvent = packageEvents.PackagePublishedEvent = packageEvents.PackageEditedEvent = packageEvents.PackageCreatedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const build_ancestry_1 = requireBuildAncestry();
	const package_milestone_enums_1 = requirePackageMilestoneEnums();
	packageEvents.PackageCreatedEvent = (0, event_1.createEventClass)('PackageCreatedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    manufacturerPartNumber: zod_1.z.string(),
	    packageFamilyId: zod_1.z.string().nullable(),
	    initialSigmaModelType: zod_1.z.string(),
	    initialPinCountEstimate: zod_1.z.number().int().nullable(),
	    hasDatasheetFileAtCreation: zod_1.z.boolean(),
	    origin: zod_1.z.nativeEnum(package_milestone_enums_1.PackageCreationOrigin),
	}));
	packageEvents.PackageEditedEvent = (0, event_1.createEventClass)('PackageEditedEvent', zod_1.z.object({ packageId: zod_1.z.string() }));
	packageEvents.PackagePublishedEvent = (0, event_1.createEventClass)('PackagePublishedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    autopublished: zod_1.z.boolean().optional(),
	    sigmaModelType: zod_1.z.string(),
	    pinCount: zod_1.z.number().int().nullable().optional(),
	    manufacturerPartNumber: zod_1.z.string().optional(),
	}));
	packageEvents.PackageAutopublishedEvent = (0, event_1.createEventClass)('PackageAutopublishedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    sigmaModelType: zod_1.z.string(),
	    manufacturerPartNumber: zod_1.z.string(),
	    pinCount: zod_1.z.number().int(),
	}));
	packageEvents.PackageAutopublishSkippedEvent = (0, event_1.createEventClass)('PackageAutopublishSkippedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    sigmaModelType: zod_1.z.string(),
	    manufacturerPartNumber: zod_1.z.string(),
	    reason: zod_1.z.string(),
	    failedHeuristics: zod_1.z.array(zod_1.z.string()),
	    pinCount: zod_1.z.number().int().nullable(),
	}));
	packageEvents.PackageFamilyEditedEvent = (0, event_1.createEventClass)('PackageFamilyEditedEvent', zod_1.z.object({ packageFamilyId: zod_1.z.string() }));
	
	return packageEvents;
}

var ingestionEvents = {};

var hasRequiredIngestionEvents;

function requireIngestionEvents () {
	if (hasRequiredIngestionEvents) return ingestionEvents;
	hasRequiredIngestionEvents = 1;
	Object.defineProperty(ingestionEvents, "__esModule", { value: true });
	ingestionEvents.BomExportedEvent = ingestionEvents.ProjectModelIngestedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	ingestionEvents.ProjectModelIngestedEvent = (0, event_1.createEventClass)('ProjectModelIngestedEvent', zod_1.z.object({
	    projectModelId: zod_1.z.string(),
	    projectId: zod_1.z.string(),
	    buildId: zod_1.z.string(),
	    componentCount: zod_1.z.number(),
	    moduleCount: zod_1.z.number(),
	}));
	ingestionEvents.BomExportedEvent = (0, event_1.createEventClass)('BomExportedEvent', zod_1.z.object({
	    projectModelId: zod_1.z.string(),
	    projectId: zod_1.z.string(),
	    bomItemCount: zod_1.z.number(),
	    resolvedAsPackageCount: zod_1.z.number(),
	    packageMissingDatasheetCount: zod_1.z.number(),
	}));
	
	return ingestionEvents;
}

var packageUnderstandingEvents = {};

var hasRequiredPackageUnderstandingEvents;

function requirePackageUnderstandingEvents () {
	if (hasRequiredPackageUnderstandingEvents) return packageUnderstandingEvents;
	hasRequiredPackageUnderstandingEvents = 1;
	Object.defineProperty(packageUnderstandingEvents, "__esModule", { value: true });
	packageUnderstandingEvents.PackageUnderstandingSkippedEvent = packageUnderstandingEvents.PackageUnderstandingWizardPlanSkippedEvent = packageUnderstandingEvents.PackageCatalogueUnderstandingCompletedEvent = packageUnderstandingEvents.PackageCatalogueExactMatchDetectedEvent = packageUnderstandingEvents.PackageUnderstandingFailedEvent = packageUnderstandingEvents.PackageUnderstandingCompletedEvent = packageUnderstandingEvents.PackageUnderstandingStartedEvent = packageUnderstandingEvents.DatasheetEnsureCompletedEvent = packageUnderstandingEvents.DatasheetReplacementCompletedEvent = packageUnderstandingEvents.DatasheetValidationRecordedEvent = packageUnderstandingEvents.DatasheetFetchCompletedEvent = packageUnderstandingEvents.DatasheetFetchStartedEvent = packageUnderstandingEvents.PackageDatasheetSearchCompletedEvent = packageUnderstandingEvents.PackageDatasheetSearchStartedEvent = packageUnderstandingEvents.ConnectorCatalogueResolvedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const build_ancestry_1 = requireBuildAncestry();
	const package_milestone_enums_1 = requirePackageMilestoneEnums();
	const package_datasheet_validation_1 = requirePackageDatasheetValidation();
	const package_datasheet_state_1 = requirePackageDatasheetState();
	const datasheet_ensure_1 = requireDatasheetEnsure();
	packageUnderstandingEvents.ConnectorCatalogueResolvedEvent = (0, event_1.createEventClass)('ConnectorCatalogueResolvedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    projectModuleId: zod_1.z.string(),
	    sigmaModelId: zod_1.z.string(),
	    pinNames: zod_1.z.array(zod_1.z.string()),
	}));
	packageUnderstandingEvents.PackageDatasheetSearchStartedEvent = (0, event_1.createEventClass)('PackageDatasheetSearchStartedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    mpn: zod_1.z.string(),
	    hasSeedUrl: zod_1.z.boolean(),
	}));
	packageUnderstandingEvents.PackageDatasheetSearchCompletedEvent = (0, event_1.createEventClass)('PackageDatasheetSearchCompletedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    mpn: zod_1.z.string(),
	    outcome: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetSearchOutcome),
	    source: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetSearchSource),
	    fileId: zod_1.z.string().nullable(),
	}));
	packageUnderstandingEvents.DatasheetFetchStartedEvent = (0, event_1.createEventClass)('DatasheetFetchStartedEvent', zod_1.z.object({
	    contextKind: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetContextKind),
	    contextId: zod_1.z.string(),
	    mpn: zod_1.z.string(),
	    hasSeedUrl: zod_1.z.boolean(),
	}));
	packageUnderstandingEvents.DatasheetFetchCompletedEvent = (0, event_1.createEventClass)('DatasheetFetchCompletedEvent', zod_1.z.object({
	    contextKind: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetContextKind),
	    contextId: zod_1.z.string(),
	    mpn: zod_1.z.string(),
	    outcome: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetSearchOutcome),
	    source: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetSearchSource),
	    fileId: zod_1.z.string().nullable(),
	    deadEnd: zod_1.z.nativeEnum(package_milestone_enums_1.DatasheetUrlDeadEnd).optional(),
	}));
	packageUnderstandingEvents.DatasheetValidationRecordedEvent = (0, event_1.createEventClass)('DatasheetValidationRecordedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    fileId: zod_1.z.string(),
	    mpn: zod_1.z.string(),
	    matches: zod_1.z.boolean(),
	    kind: zod_1.z.nativeEnum(package_datasheet_validation_1.DatasheetValidationVerdictKind),
	}));
	packageUnderstandingEvents.DatasheetReplacementCompletedEvent = (0, event_1.createEventClass)('DatasheetReplacementCompletedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    mpn: zod_1.z.string(),
	    rejectedFileId: zod_1.z.string(),
	    rejectedKind: zod_1.z.nativeEnum(package_datasheet_validation_1.DatasheetValidationVerdictKind).optional(),
	    outcome: zod_1.z.nativeEnum(package_datasheet_validation_1.DatasheetReplacementOutcome),
	    candidateFileId: zod_1.z.string().nullable(),
	}));
	packageUnderstandingEvents.DatasheetEnsureCompletedEvent = (0, event_1.createEventClass)('DatasheetEnsureCompletedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    requestReason: zod_1.z.nativeEnum(datasheet_ensure_1.DatasheetEnsureRequestReason),
	    outcome: zod_1.z.nativeEnum(datasheet_ensure_1.DatasheetEnsureOutcome),
	    reason: zod_1.z.nativeEnum(package_datasheet_state_1.DatasheetStateReason).optional(),
	    source: zod_1.z.nativeEnum(datasheet_ensure_1.DatasheetEnsureSource),
	    attempt: zod_1.z.number().int().optional(),
	    fileId: zod_1.z.string().nullable(),
	}));
	packageUnderstandingEvents.PackageUnderstandingStartedEvent = (0, event_1.createEventClass)('PackageUnderstandingStartedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    workflowId: zod_1.z.string().nullable(),
	    hasDatasheet: zod_1.z.boolean(),
	    hasCatalogueItems: zod_1.z.boolean(),
	    sigmaModelType: zod_1.z.string(),
	    pinCount: zod_1.z.number().int().nullable(),
	}));
	packageUnderstandingEvents.PackageUnderstandingCompletedEvent = (0, event_1.createEventClass)('PackageUnderstandingCompletedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    workflowId: zod_1.z.string().nullable(),
	    outcome: zod_1.z.nativeEnum(package_milestone_enums_1.PackageUnderstandingOutcome),
	    failedSteps: zod_1.z.array(zod_1.z.string()).optional(),
	    sigmaModelType: zod_1.z.string(),
	    pinCount: zod_1.z.number().int().nullable(),
	    signalCount: zod_1.z.number().int(),
	}));
	packageUnderstandingEvents.PackageUnderstandingFailedEvent = (0, event_1.createEventClass)('PackageUnderstandingFailedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    workflowId: zod_1.z.string().nullable(),
	    errorMessage: zod_1.z.string(),
	    sigmaModelType: zod_1.z.string(),
	    pinCount: zod_1.z.number().int().nullable(),
	}));
	packageUnderstandingEvents.PackageCatalogueExactMatchDetectedEvent = (0, event_1.createEventClass)('PackageCatalogueExactMatchDetectedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    catalogueName: zod_1.z.string(),
	    catalogueMpn: zod_1.z.string(),
	    catalogueManufacturerName: zod_1.z.string(),
	    hasDatasheetUrl: zod_1.z.boolean(),
	}));
	packageUnderstandingEvents.PackageCatalogueUnderstandingCompletedEvent = (0, event_1.createEventClass)('PackageCatalogueUnderstandingCompletedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    resolutionGenerated: zod_1.z.boolean(),
	    skippedReason: zod_1.z.string().nullable(),
	    sigmaModelTypeAfter: zod_1.z.string(),
	    pinCount: zod_1.z.number().int().nullable(),
	}));
	packageUnderstandingEvents.PackageUnderstandingWizardPlanSkippedEvent = (0, event_1.createEventClass)('PackageUnderstandingWizardPlanSkippedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    planSkipReason: zod_1.z.string(),
	}));
	packageUnderstandingEvents.PackageUnderstandingSkippedEvent = (0, event_1.createEventClass)('PackageUnderstandingSkippedEvent', zod_1.z.object({
	    ...build_ancestry_1.buildAncestryShape,
	    packageId: zod_1.z.string(),
	    skipCause: zod_1.z.nativeEnum(package_milestone_enums_1.PackageUnderstandingSkipCause),
	    hasDatasheet: zod_1.z.boolean().optional(),
	    hasCatalogueItems: zod_1.z.boolean().optional(),
	}));
	
	return packageUnderstandingEvents;
}

var agenticExtractionEvents = {};

var hasRequiredAgenticExtractionEvents;

function requireAgenticExtractionEvents () {
	if (hasRequiredAgenticExtractionEvents) return agenticExtractionEvents;
	hasRequiredAgenticExtractionEvents = 1;
	Object.defineProperty(agenticExtractionEvents, "__esModule", { value: true });
	agenticExtractionEvents.AgenticExtractionFailedEvent = agenticExtractionEvents.AgenticExtractionCompletedEvent = agenticExtractionEvents.AgenticExtractionStartedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const AgenticExtraction_1 = requireAgenticExtraction();
	const agenticExtractionEventBase = {
	    extractor: zod_1.z.nativeEnum(AgenticExtraction_1.AgenticExtractorName),
	    extractorVersion: zod_1.z.number().int(),
	    packageId: zod_1.z.string(),
	    fileId: zod_1.z.string(),
	    runId: zod_1.z.string(),
	};
	agenticExtractionEvents.AgenticExtractionStartedEvent = (0, event_1.createEventClass)('AgenticExtractionStartedEvent', zod_1.z.object({ ...agenticExtractionEventBase }));
	agenticExtractionEvents.AgenticExtractionCompletedEvent = (0, event_1.createEventClass)('AgenticExtractionCompletedEvent', zod_1.z.object({ ...agenticExtractionEventBase, outcome: zod_1.z.nativeEnum(AgenticExtraction_1.AgenticExtractionRunStatus) }));
	agenticExtractionEvents.AgenticExtractionFailedEvent = (0, event_1.createEventClass)('AgenticExtractionFailedEvent', zod_1.z.object({ ...agenticExtractionEventBase, errorMessage: zod_1.z.string() }));
	
	return agenticExtractionEvents;
}

var packageDataAuditEvents = {};

var hasRequiredPackageDataAuditEvents;

function requirePackageDataAuditEvents () {
	if (hasRequiredPackageDataAuditEvents) return packageDataAuditEvents;
	hasRequiredPackageDataAuditEvents = 1;
	Object.defineProperty(packageDataAuditEvents, "__esModule", { value: true });
	packageDataAuditEvents.PackageDataAuditFailedEvent = packageDataAuditEvents.PackageDataAuditCompletedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const edit_provenance_types_1 = requireEditProvenance_types();
	const property_key_1 = requirePropertyKey();
	const slice_key_1 = requireSliceKey();
	const package_data_audit_types_1 = requirePackageDataAudit_types();
	const packageDataAuditEventBase = {
	    auditId: zod_1.z.string(),
	    packageId: zod_1.z.string(),
	    propertyKey: zod_1.z.nativeEnum(property_key_1.PropertyKey),
	    channel: zod_1.z.nativeEnum(edit_provenance_types_1.EditAgency),
	    actorUserId: zod_1.z.string().optional(),
	    conversationId: zod_1.z.string().optional(),
	    parentConversationId: zod_1.z.string().optional(),
	    projectId: zod_1.z.string().optional(),
	    buildId: zod_1.z.string().optional(),
	    agentId: zod_1.z.string().optional(),
	    durationMs: zod_1.z.number().int(),
	    itemCount: zod_1.z.number().int(),
	};
	const packageDataAuditEventItem = zod_1.z.object({
	    status: zod_1.z.nativeEnum(package_data_audit_types_1.PackageDataAuditItemStatus),
	    subject: zod_1.z.string(),
	    opinionValue: zod_1.z.string().optional(),
	});
	packageDataAuditEvents.PackageDataAuditCompletedEvent = (0, event_1.createEventClass)('PackageDataAuditCompletedEvent', zod_1.z.object({
	    ...packageDataAuditEventBase,
	    status: zod_1.z.nativeEnum(package_data_audit_types_1.PackageDataAuditStatus),
	    sliceKey: zod_1.z.nativeEnum(slice_key_1.SliceKey).optional(),
	    sliceVersion: zod_1.z.number().int().optional(),
	    adjudicatedCount: zod_1.z.number().int(),
	    pendingCount: zod_1.z.number().int(),
	    notAdjudicatedCount: zod_1.z.number().int(),
	    dispatched: zod_1.z.boolean(),
	    requestIds: zod_1.z.string().array(),
	    planIds: zod_1.z.string().array(),
	    opinionCount: zod_1.z.number().int(),
	    items: packageDataAuditEventItem.array(),
	}));
	packageDataAuditEvents.PackageDataAuditFailedEvent = (0, event_1.createEventClass)('PackageDataAuditFailedEvent', zod_1.z.object({ ...packageDataAuditEventBase, errorMessage: zod_1.z.string() }));
	
	return packageDataAuditEvents;
}

var customerBomMergeEvents = {};

var customerBomMergeEnums = {};

var hasRequiredCustomerBomMergeEnums;

function requireCustomerBomMergeEnums () {
	if (hasRequiredCustomerBomMergeEnums) return customerBomMergeEnums;
	hasRequiredCustomerBomMergeEnums = 1;
	Object.defineProperty(customerBomMergeEnums, "__esModule", { value: true });
	customerBomMergeEnums.CustomerBomMergeUploadUnusedReason = customerBomMergeEnums.CustomerBomMergeFailurePhase = void 0;
	var CustomerBomMergeFailurePhase;
	(function (CustomerBomMergeFailurePhase) {
	    CustomerBomMergeFailurePhase["Merge"] = "merge";
	    CustomerBomMergeFailurePhase["Enrichment"] = "enrichment";
	})(CustomerBomMergeFailurePhase || (customerBomMergeEnums.CustomerBomMergeFailurePhase = CustomerBomMergeFailurePhase = {}));
	var CustomerBomMergeUploadUnusedReason;
	(function (CustomerBomMergeUploadUnusedReason) {
	    CustomerBomMergeUploadUnusedReason["PackageHasDatasheet"] = "package_has_datasheet";
	    CustomerBomMergeUploadUnusedReason["NoIdentity"] = "no_identity";
	    CustomerBomMergeUploadUnusedReason["OwnershipConflict"] = "ownership_conflict";
	    CustomerBomMergeUploadUnusedReason["DatasheetMissing"] = "datasheet_missing";
	    CustomerBomMergeUploadUnusedReason["AnotherUploadChosen"] = "another_upload_chosen";
	    CustomerBomMergeUploadUnusedReason["ManufacturerUnreadable"] = "manufacturer_unreadable";
	})(CustomerBomMergeUploadUnusedReason || (customerBomMergeEnums.CustomerBomMergeUploadUnusedReason = CustomerBomMergeUploadUnusedReason = {}));
	
	return customerBomMergeEnums;
}

var hasRequiredCustomerBomMergeEvents;

function requireCustomerBomMergeEvents () {
	if (hasRequiredCustomerBomMergeEvents) return customerBomMergeEvents;
	hasRequiredCustomerBomMergeEvents = 1;
	Object.defineProperty(customerBomMergeEvents, "__esModule", { value: true });
	customerBomMergeEvents.CustomerBomMergeUploadedDatasheetUnusedEvent = customerBomMergeEvents.CustomerBomMergeFailedEvent = customerBomMergeEvents.CustomerBomMergeCompletedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const customer_bom_merge_enums_1 = requireCustomerBomMergeEnums();
	customerBomMergeEvents.CustomerBomMergeCompletedEvent = (0, event_1.createEventClass)('CustomerBomMergeCompletedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    newPackagesCreated: zod_1.z.number(),
	    bomItemsResolvedBefore: zod_1.z.number(),
	    bomItemsResolvedAfter: zod_1.z.number(),
	    moduleGroupsResolvedBefore: zod_1.z.number(),
	    moduleGroupsResolvedAfter: zod_1.z.number(),
	    moduleGroupsWithChangedPackageId: zod_1.z.number(),
	}));
	customerBomMergeEvents.CustomerBomMergeFailedEvent = (0, event_1.createEventClass)('CustomerBomMergeFailedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    phase: zod_1.z.nativeEnum(customer_bom_merge_enums_1.CustomerBomMergeFailurePhase),
	    errorName: zod_1.z.string(),
	    errorMessage: zod_1.z.string(),
	}));
	customerBomMergeEvents.CustomerBomMergeUploadedDatasheetUnusedEvent = (0, event_1.createEventClass)('CustomerBomMergeUploadedDatasheetUnusedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    fileId: zod_1.z.string(),
	    packageId: zod_1.z.string().nullable(),
	    bomItemCount: zod_1.z.number(),
	    reason: zod_1.z.nativeEnum(customer_bom_merge_enums_1.CustomerBomMergeUploadUnusedReason),
	}));
	
	return customerBomMergeEvents;
}

var fastModeEvents = {};

var buildStatus = {};

var hasRequiredBuildStatus;

function requireBuildStatus () {
	if (hasRequiredBuildStatus) return buildStatus;
	hasRequiredBuildStatus = 1;
	Object.defineProperty(buildStatus, "__esModule", { value: true });
	buildStatus.BUILD_STATUSES = buildStatus.buildTimeoutReasonSchema = buildStatus.BuildTimeoutReason = buildStatus.buildStatusSchema = buildStatus.BuildStatus = void 0;
	buildStatus.isBuildStatus = isBuildStatus;
	buildStatus.isActiveProcessingStatus = isActiveProcessingStatus;
	buildStatus.isFinalBuildStatus = isFinalBuildStatus;
	buildStatus.isBomReadyOrLaterStatus = isBomReadyOrLaterStatus;
	const z = /*@__PURE__*/ requireZod();
	var BuildStatus;
	(function (BuildStatus) {
	    BuildStatus["Pending"] = "pending";
	    BuildStatus["Validating"] = "validating";
	    BuildStatus["Processing"] = "processing";
	    BuildStatus["BomAwaitingReview"] = "bom_awaiting_review";
	    BuildStatus["ProcessingBom"] = "processing_bom";
	    BuildStatus["ReadyToAnalyze"] = "ready_to_analyze";
	    BuildStatus["ProcessingDesign"] = "processing_design";
	    BuildStatus["AnalysisComplete"] = "analysis_complete";
	    BuildStatus["Complete"] = "complete";
	    BuildStatus["Error"] = "error";
	    BuildStatus["Abandoned"] = "abandoned";
	    BuildStatus["TimedOut"] = "timed_out";
	    BuildStatus["CreditRequired"] = "credit_required";
	    BuildStatus["Unknown"] = "unknown";
	})(BuildStatus || (buildStatus.BuildStatus = BuildStatus = {}));
	buildStatus.buildStatusSchema = z.nativeEnum(BuildStatus);
	var BuildTimeoutReason;
	(function (BuildTimeoutReason) {
	    BuildTimeoutReason["ParseWait"] = "parse_wait";
	    BuildTimeoutReason["NetlistWait"] = "netlist_wait";
	})(BuildTimeoutReason || (buildStatus.BuildTimeoutReason = BuildTimeoutReason = {}));
	buildStatus.buildTimeoutReasonSchema = z.nativeEnum(BuildTimeoutReason);
	function isBuildStatus(value) {
	    return Object.values(BuildStatus).includes(value);
	}
	buildStatus.BUILD_STATUSES = Object.values(BuildStatus);
	function isActiveProcessingStatus(status) {
	    switch (status) {
	        case BuildStatus.Pending:
	        case BuildStatus.Validating:
	        case BuildStatus.Processing:
	        case BuildStatus.ProcessingBom:
	        case BuildStatus.ProcessingDesign:
	            return true;
	    }
	    return false;
	}
	function isFinalBuildStatus(status) {
	    switch (status) {
	        case BuildStatus.Complete:
	        case BuildStatus.Error:
	        case BuildStatus.Abandoned:
	        case BuildStatus.TimedOut:
	        case BuildStatus.CreditRequired:
	            return true;
	    }
	    return false;
	}
	function isBomReadyOrLaterStatus(status) {
	    switch (status) {
	        case BuildStatus.BomAwaitingReview:
	        case BuildStatus.ProcessingBom:
	        case BuildStatus.ReadyToAnalyze:
	        case BuildStatus.ProcessingDesign:
	        case BuildStatus.AnalysisComplete:
	        case BuildStatus.Complete:
	            return true;
	    }
	    return false;
	}
	
	return buildStatus;
}

var hasRequiredFastModeEvents;

function requireFastModeEvents () {
	if (hasRequiredFastModeEvents) return fastModeEvents;
	hasRequiredFastModeEvents = 1;
	Object.defineProperty(fastModeEvents, "__esModule", { value: true });
	fastModeEvents.FastModeBuildDemotedEvent = fastModeEvents.FastModeBuildCompletedEvent = fastModeEvents.FastModeAnalysisDispatchedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const analysis_type_1 = requireAnalysisType();
	const build_status_1 = requireBuildStatus();
	const event_1 = requireEvent();
	fastModeEvents.FastModeAnalysisDispatchedEvent = (0, event_1.createEventClass)('FastModeAnalysisDispatchedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string(),
	    organizationId: zod_1.z.string(),
	    workflowId: zod_1.z.string(),
	}));
	fastModeEvents.FastModeBuildCompletedEvent = (0, event_1.createEventClass)('FastModeBuildCompletedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    analysisType: analysis_type_1.AnalysisTypeSchema.optional(),
	    resultCount: zod_1.z.number().optional(),
	    aiStagesCompleted: zod_1.z.number().optional(),
	    aiSubjectsProcessed: zod_1.z.number().optional(),
	    aiSubjectsFailed: zod_1.z.number().optional(),
	}));
	fastModeEvents.FastModeBuildDemotedEvent = (0, event_1.createEventClass)('FastModeBuildDemotedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    reason: zod_1.z.string(),
	    parkedStatus: build_status_1.buildStatusSchema,
	}));
	
	return fastModeEvents;
}

var buildLifecycleEvents = {};

var buildLifecycleEnums = {};

var hasRequiredBuildLifecycleEnums;

function requireBuildLifecycleEnums () {
	if (hasRequiredBuildLifecycleEnums) return buildLifecycleEnums;
	hasRequiredBuildLifecycleEnums = 1;
	Object.defineProperty(buildLifecycleEnums, "__esModule", { value: true });
	buildLifecycleEnums.ResultsOpenSource = buildLifecycleEnums.NetlistValidationOutcome = buildLifecycleEnums.ParseResolutionAction = buildLifecycleEnums.ParseFailurePhase = buildLifecycleEnums.UploadValidationFailureReason = buildLifecycleEnums.BuildTransitionTrigger = void 0;
	var BuildTransitionTrigger;
	(function (BuildTransitionTrigger) {
	    BuildTransitionTrigger["System"] = "system";
	    BuildTransitionTrigger["StaffOverride"] = "staff_override";
	    BuildTransitionTrigger["Customer"] = "customer";
	})(BuildTransitionTrigger || (buildLifecycleEnums.BuildTransitionTrigger = BuildTransitionTrigger = {}));
	var UploadValidationFailureReason;
	(function (UploadValidationFailureReason) {
	    UploadValidationFailureReason["Validation"] = "validation";
	    UploadValidationFailureReason["Exception"] = "exception";
	})(UploadValidationFailureReason || (buildLifecycleEnums.UploadValidationFailureReason = UploadValidationFailureReason = {}));
	var ParseFailurePhase;
	(function (ParseFailurePhase) {
	    ParseFailurePhase["Parse"] = "parse";
	    ParseFailurePhase["Ingest"] = "ingest";
	})(ParseFailurePhase || (buildLifecycleEnums.ParseFailurePhase = ParseFailurePhase = {}));
	var ParseResolutionAction;
	(function (ParseResolutionAction) {
	    ParseResolutionAction["Retried"] = "retried";
	    ParseResolutionAction["Bypassed"] = "bypassed";
	    ParseResolutionAction["TimedOut"] = "timed_out";
	})(ParseResolutionAction || (buildLifecycleEnums.ParseResolutionAction = ParseResolutionAction = {}));
	var NetlistValidationOutcome;
	(function (NetlistValidationOutcome) {
	    NetlistValidationOutcome["Valid"] = "valid";
	    NetlistValidationOutcome["Invalid"] = "invalid";
	    NetlistValidationOutcome["NoNetlist"] = "no_netlist";
	    NetlistValidationOutcome["Unparseable"] = "unparseable";
	})(NetlistValidationOutcome || (buildLifecycleEnums.NetlistValidationOutcome = NetlistValidationOutcome = {}));
	var ResultsOpenSource;
	(function (ResultsOpenSource) {
	    ResultsOpenSource["DeliveryLink"] = "delivery_link";
	    ResultsOpenSource["Direct"] = "direct";
	})(ResultsOpenSource || (buildLifecycleEnums.ResultsOpenSource = ResultsOpenSource = {}));
	
	return buildLifecycleEnums;
}

var netlistGate = {};

var hasRequiredNetlistGate;

function requireNetlistGate () {
	if (hasRequiredNetlistGate) return netlistGate;
	hasRequiredNetlistGate = 1;
	Object.defineProperty(netlistGate, "__esModule", { value: true });
	netlistGate.NetlistSource = netlistGate.NetlistGateState = netlistGate.NetlistClearanceOutcome = void 0;
	var NetlistClearanceOutcome;
	(function (NetlistClearanceOutcome) {
	    NetlistClearanceOutcome["Validated"] = "validated";
	    NetlistClearanceOutcome["ErrorsAccepted"] = "errors_accepted";
	    NetlistClearanceOutcome["NoGenerator"] = "no_generator";
	})(NetlistClearanceOutcome || (netlistGate.NetlistClearanceOutcome = NetlistClearanceOutcome = {}));
	var NetlistGateState;
	(function (NetlistGateState) {
	    NetlistGateState["NotArmed"] = "not_armed";
	    NetlistGateState["Pending"] = "pending";
	    NetlistGateState["Cleared"] = "cleared";
	})(NetlistGateState || (netlistGate.NetlistGateState = NetlistGateState = {}));
	var NetlistSource;
	(function (NetlistSource) {
	    NetlistSource["Generated"] = "generated";
	    NetlistSource["Uploaded"] = "uploaded";
	})(NetlistSource || (netlistGate.NetlistSource = NetlistSource = {}));
	
	return netlistGate;
}

var netlistTriage = {};

var hasRequiredNetlistTriage;

function requireNetlistTriage () {
	if (hasRequiredNetlistTriage) return netlistTriage;
	hasRequiredNetlistTriage = 1;
	Object.defineProperty(netlistTriage, "__esModule", { value: true });
	netlistTriage.NetlistTriageLocations = netlistTriage.NetlistTriagePointerSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const ServiceLocation_1 = requireServiceLocation();
	const BUILD_BLOB_SOURCE = 'userdata';
	netlistTriage.NetlistTriagePointerSchema = zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string(),
	    organizationId: zod_1.z.string(),
	    cadTool: zod_1.z.string().optional(),
	    workflowId: zod_1.z.string(),
	    uploadsPrefix: zod_1.z.string(),
	    validationResultKey: zod_1.z.string(),
	    bundleKey: zod_1.z.string().optional(),
	    bundleUnavailableReason: zod_1.z.string().optional(),
	    pipelineOpsUrl: zod_1.z.string().optional(),
	});
	class NetlistTriageLocations {
	    static uploadsPrefix(organizationId, projectId, buildId) {
	        return (0, ServiceLocation_1.createLocationString)(BUILD_BLOB_SOURCE, `${organizationId}/projects/${projectId}/builds/${buildId}/uploads/`);
	    }
	    static validationResultKey(organizationId, projectId, buildId) {
	        return (0, ServiceLocation_1.createLocationString)(BUILD_BLOB_SOURCE, `${organizationId}/projects/${projectId}/builds/${buildId}/netlistValidation.json`);
	    }
	    static bundleKey(organizationId, projectId, buildId, runId) {
	        return (0, ServiceLocation_1.createLocationString)(BUILD_BLOB_SOURCE, `${organizationId}/projects/${projectId}/builds/${buildId}/netlist-generation/triage-${runId}.tar.gz`);
	    }
	    static pipelineOpsUrl(appBaseUrl, buildId) {
	        return `${appBaseUrl.replace(/\/+$/, '')}/tools/admin/pipeline-ops?buildId=${encodeURIComponent(buildId)}`;
	    }
	}
	netlistTriage.NetlistTriageLocations = NetlistTriageLocations;
	
	return netlistTriage;
}

var hasRequiredBuildLifecycleEvents;

function requireBuildLifecycleEvents () {
	if (hasRequiredBuildLifecycleEvents) return buildLifecycleEvents;
	hasRequiredBuildLifecycleEvents = 1;
	Object.defineProperty(buildLifecycleEvents, "__esModule", { value: true });
	buildLifecycleEvents.BuildSupersededEvent = buildLifecycleEvents.BuildAbandonedEvent = buildLifecycleEvents.ResultsOpenedEvent = buildLifecycleEvents.BuildDeliveredEvent = buildLifecycleEvents.BomReviewSentEvent = buildLifecycleEvents.BomResolutionSummaryEvent = buildLifecycleEvents.NetlistErrorsAcceptedEvent = buildLifecycleEvents.NetlistGenerationFailedEvent = buildLifecycleEvents.NetlistValidationCompletedEvent = buildLifecycleEvents.BuildParseResolutionEvent = buildLifecycleEvents.BuildParseFailedEvent = buildLifecycleEvents.BuildUploadValidationFailedEvent = buildLifecycleEvents.BuildUploadAcceptedEvent = buildLifecycleEvents.BuildStageTransitionedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const build_lifecycle_enums_1 = requireBuildLifecycleEnums();
	const netlist_gate_1 = requireNetlistGate();
	const remediation_1 = requireRemediation$1();
	const netlist_triage_1 = requireNetlistTriage();
	buildLifecycleEvents.BuildStageTransitionedEvent = (0, event_1.createEventClass)('BuildStageTransitionedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    fromStatus: zod_1.z.string(),
	    toStatus: zod_1.z.string(),
	    statusMessage: zod_1.z.string().optional(),
	    actorUserId: zod_1.z.string().optional(),
	    trigger: zod_1.z.nativeEnum(build_lifecycle_enums_1.BuildTransitionTrigger),
	}));
	buildLifecycleEvents.BuildUploadAcceptedEvent = (0, event_1.createEventClass)('BuildUploadAcceptedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    userId: zod_1.z.string(),
	    declaredTool: zod_1.z.string().optional(),
	}));
	buildLifecycleEvents.BuildUploadValidationFailedEvent = (0, event_1.createEventClass)('BuildUploadValidationFailedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    reason: zod_1.z.nativeEnum(build_lifecycle_enums_1.UploadValidationFailureReason),
	    errorCount: zod_1.z.number().int().nonnegative(),
	    errors: zod_1.z.array(zod_1.z.string()),
	    declaredTool: zod_1.z.string().optional(),
	}));
	buildLifecycleEvents.BuildParseFailedEvent = (0, event_1.createEventClass)('BuildParseFailedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    phase: zod_1.z.nativeEnum(build_lifecycle_enums_1.ParseFailurePhase),
	    reason: zod_1.z.string(),
	    remediationOwner: zod_1.z.nativeEnum(remediation_1.RemediationOwner).optional(),
	    remediationSummary: zod_1.z.string().optional(),
	    remediationSteps: zod_1.z.array(zod_1.z.string()).optional(),
	    triage: netlist_triage_1.NetlistTriagePointerSchema.optional(),
	}));
	buildLifecycleEvents.BuildParseResolutionEvent = (0, event_1.createEventClass)('BuildParseResolutionEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    action: zod_1.z.nativeEnum(build_lifecycle_enums_1.ParseResolutionAction),
	    projectModelId: zod_1.z.string().optional(),
	}));
	buildLifecycleEvents.NetlistValidationCompletedEvent = (0, event_1.createEventClass)('NetlistValidationCompletedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    outcome: zod_1.z.nativeEnum(build_lifecycle_enums_1.NetlistValidationOutcome),
	    errorCount: zod_1.z.number().int().nonnegative(),
	    errorsByType: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().nonnegative()).optional(),
	    netlistSource: zod_1.z.nativeEnum(netlist_gate_1.NetlistSource).optional(),
	    remediationOwner: zod_1.z.nativeEnum(remediation_1.RemediationOwner).optional(),
	    remediationSummary: zod_1.z.string().optional(),
	    remediationSteps: zod_1.z.array(zod_1.z.string()).optional(),
	    triage: netlist_triage_1.NetlistTriagePointerSchema.optional(),
	}));
	buildLifecycleEvents.NetlistGenerationFailedEvent = (0, event_1.createEventClass)('NetlistGenerationFailedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    failureCode: zod_1.z.nativeEnum(remediation_1.NetlistGenerationFailureCode).optional(),
	    remediationOwner: zod_1.z.nativeEnum(remediation_1.RemediationOwner),
	    remediationSummary: zod_1.z.string(),
	    remediationSteps: zod_1.z.array(zod_1.z.string()),
	    reason: zod_1.z.string(),
	    triage: netlist_triage_1.NetlistTriagePointerSchema.optional(),
	}));
	buildLifecycleEvents.NetlistErrorsAcceptedEvent = (0, event_1.createEventClass)('NetlistErrorsAcceptedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    acceptedByUserId: zod_1.z.string(),
	    workflowId: zod_1.z.string(),
	}));
	buildLifecycleEvents.BomResolutionSummaryEvent = (0, event_1.createEventClass)('BomResolutionSummaryEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    resolutionTypeCounts: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().nonnegative()),
	    unresolvedModuleCount: zod_1.z.number().int().nonnegative(),
	    dispatchedModuleCount: zod_1.z.number().int().nonnegative(),
	}));
	buildLifecycleEvents.BomReviewSentEvent = (0, event_1.createEventClass)('BomReviewSentEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    bomItemCount: zod_1.z.number().int().nonnegative(),
	    reviewRowCount: zod_1.z.number().int().nonnegative(),
	    importantRowCount: zod_1.z.number().int().nonnegative(),
	    noDatasheetRowCount: zod_1.z.number().int().nonnegative(),
	}));
	buildLifecycleEvents.BuildDeliveredEvent = (0, event_1.createEventClass)('BuildDeliveredEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    deliveredByUserId: zod_1.z.string().optional(),
	}));
	buildLifecycleEvents.ResultsOpenedEvent = (0, event_1.createEventClass)('ResultsOpenedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    openedByUserId: zod_1.z.string(),
	    source: zod_1.z.nativeEnum(build_lifecycle_enums_1.ResultsOpenSource),
	    deliveryLinkToken: zod_1.z.string().optional(),
	    firstOpen: zod_1.z.boolean(),
	}));
	buildLifecycleEvents.BuildAbandonedEvent = (0, event_1.createEventClass)('BuildAbandonedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    abandonedByUserId: zod_1.z.string().optional(),
	    wasSuperseded: zod_1.z.boolean(),
	}));
	buildLifecycleEvents.BuildSupersededEvent = (0, event_1.createEventClass)('BuildSupersededEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    supersededByBuildId: zod_1.z.string(),
	}));
	
	return buildLifecycleEvents;
}

var analysisEvents = {};

var hasRequiredAnalysisEvents;

function requireAnalysisEvents () {
	if (hasRequiredAnalysisEvents) return analysisEvents;
	hasRequiredAnalysisEvents = 1;
	Object.defineProperty(analysisEvents, "__esModule", { value: true });
	analysisEvents.AnalysisRunFailedEvent = analysisEvents.AnalysisRunCompletedEvent = analysisEvents.AnalysisRunStartedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	analysisEvents.AnalysisRunStartedEvent = (0, event_1.createEventClass)('AnalysisRunStartedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    analysisType: zod_1.z.string(),
	    workflowId: zod_1.z.string().optional(),
	}));
	analysisEvents.AnalysisRunCompletedEvent = (0, event_1.createEventClass)('AnalysisRunCompletedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    analysisType: zod_1.z.string(),
	    workflowId: zod_1.z.string().optional(),
	    resultCount: zod_1.z.number().int().nonnegative().optional(),
	    startedAt: zod_1.z.string(),
	    completedAt: zod_1.z.string(),
	}));
	analysisEvents.AnalysisRunFailedEvent = (0, event_1.createEventClass)('AnalysisRunFailedEvent', zod_1.z.object({
	    buildId: zod_1.z.string(),
	    projectId: zod_1.z.string().optional(),
	    organizationId: zod_1.z.string().optional(),
	    analysisType: zod_1.z.string(),
	    workflowId: zod_1.z.string().optional(),
	    errorMessage: zod_1.z.string().optional(),
	    startedAt: zod_1.z.string(),
	    completedAt: zod_1.z.string(),
	}));
	
	return analysisEvents;
}

var bomResolutionEnums = {};

var hasRequiredBomResolutionEnums;

function requireBomResolutionEnums () {
	if (hasRequiredBomResolutionEnums) return bomResolutionEnums;
	hasRequiredBomResolutionEnums = 1;
	Object.defineProperty(bomResolutionEnums, "__esModule", { value: true });
	bomResolutionEnums.AutoReviewRejectionReason = bomResolutionEnums.AutoReviewOutcome = bomResolutionEnums.BomItemResolutionCatalogue = bomResolutionEnums.BomItemResolutionSource = bomResolutionEnums.BomItemResolutionOutcome = bomResolutionEnums.BomItemIdentifierClass = void 0;
	var BomItemIdentifierClass;
	(function (BomItemIdentifierClass) {
	    BomItemIdentifierClass["Mpn"] = "mpn";
	    BomItemIdentifierClass["SupplierPnOnly"] = "supplier_pn_only";
	    BomItemIdentifierClass["None"] = "none";
	    BomItemIdentifierClass["NotExtracted"] = "not_extracted";
	})(BomItemIdentifierClass || (bomResolutionEnums.BomItemIdentifierClass = BomItemIdentifierClass = {}));
	var BomItemResolutionOutcome;
	(function (BomItemResolutionOutcome) {
	    BomItemResolutionOutcome["Package"] = "package";
	    BomItemResolutionOutcome["SigmaModel"] = "sigma_model";
	    BomItemResolutionOutcome["Unresolved"] = "unresolved";
	    BomItemResolutionOutcome["NonElectrical"] = "non_electrical";
	})(BomItemResolutionOutcome || (bomResolutionEnums.BomItemResolutionOutcome = BomItemResolutionOutcome = {}));
	var BomItemResolutionSource;
	(function (BomItemResolutionSource) {
	    BomItemResolutionSource["Fresh"] = "fresh";
	    BomItemResolutionSource["ReusedOwned"] = "reused_owned";
	    BomItemResolutionSource["ReusedCopy"] = "reused_copy";
	})(BomItemResolutionSource || (bomResolutionEnums.BomItemResolutionSource = BomItemResolutionSource = {}));
	var BomItemResolutionCatalogue;
	(function (BomItemResolutionCatalogue) {
	    BomItemResolutionCatalogue["Digikey"] = "digikey";
	    BomItemResolutionCatalogue["Octopart"] = "octopart";
	    BomItemResolutionCatalogue["UltraLibrarian"] = "ultralibrarian";
	    BomItemResolutionCatalogue["ParentDeployment"] = "parent_deployment";
	    BomItemResolutionCatalogue["PreviousBuild"] = "previous_build";
	    BomItemResolutionCatalogue["None"] = "none";
	})(BomItemResolutionCatalogue || (bomResolutionEnums.BomItemResolutionCatalogue = BomItemResolutionCatalogue = {}));
	var AutoReviewOutcome;
	(function (AutoReviewOutcome) {
	    AutoReviewOutcome["Approved"] = "approved";
	    AutoReviewOutcome["Rejected"] = "rejected";
	})(AutoReviewOutcome || (bomResolutionEnums.AutoReviewOutcome = AutoReviewOutcome = {}));
	var AutoReviewRejectionReason;
	(function (AutoReviewRejectionReason) {
	    AutoReviewRejectionReason["PackageDraft"] = "package_draft";
	    AutoReviewRejectionReason["NoValidator"] = "no_validator";
	    AutoReviewRejectionReason["ParameterMissing"] = "parameter_missing";
	    AutoReviewRejectionReason["ParameterOutOfRange"] = "parameter_out_of_range";
	    AutoReviewRejectionReason["SignalsNotMapped"] = "signals_not_mapped";
	    AutoReviewRejectionReason["Other"] = "other";
	})(AutoReviewRejectionReason || (bomResolutionEnums.AutoReviewRejectionReason = AutoReviewRejectionReason = {}));
	
	return bomResolutionEnums;
}

var bomResolutionEvents = {};

var hasRequiredBomResolutionEvents;

function requireBomResolutionEvents () {
	if (hasRequiredBomResolutionEvents) return bomResolutionEvents;
	hasRequiredBomResolutionEvents = 1;
	Object.defineProperty(bomResolutionEvents, "__esModule", { value: true });
	bomResolutionEvents.BomAutoReviewCompletedEvent = bomResolutionEvents.BomItemAutoReviewedEvent = bomResolutionEvents.BomItemResolvedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const bom_resolution_enums_1 = requireBomResolutionEnums();
	bomResolutionEvents.BomItemResolvedEvent = (0, event_1.createEventClass)('BomItemResolvedEvent', zod_1.z.object({
	    projectModuleId: zod_1.z.string(),
	    source: zod_1.z.nativeEnum(bom_resolution_enums_1.BomItemResolutionSource),
	    identifierClass: zod_1.z.nativeEnum(bom_resolution_enums_1.BomItemIdentifierClass),
	    outcome: zod_1.z.nativeEnum(bom_resolution_enums_1.BomItemResolutionOutcome),
	    catalogue: zod_1.z.nativeEnum(bom_resolution_enums_1.BomItemResolutionCatalogue),
	    packageId: zod_1.z.string().nullable(),
	    sigmaModelId: zod_1.z.string().nullable(),
	}));
	bomResolutionEvents.BomItemAutoReviewedEvent = (0, event_1.createEventClass)('BomItemAutoReviewedEvent', zod_1.z.object({
	    bomItemId: zod_1.z.string(),
	    referenceDesignator: zod_1.z.string().nullable(),
	    resolutionType: zod_1.z.string().nullable(),
	    sigmaModelType: zod_1.z.string().nullable(),
	    outcome: zod_1.z.nativeEnum(bom_resolution_enums_1.AutoReviewOutcome),
	    rejectionReason: zod_1.z.nativeEnum(bom_resolution_enums_1.AutoReviewRejectionReason).optional(),
	}));
	bomResolutionEvents.BomAutoReviewCompletedEvent = (0, event_1.createEventClass)('BomAutoReviewCompletedEvent', zod_1.z.object({
	    projectModelId: zod_1.z.string().nullable(),
	    totalCount: zod_1.z.number(),
	    newlyReviewedCount: zod_1.z.number(),
	    alreadyReviewedCount: zod_1.z.number(),
	    remainsUnreviewedCount: zod_1.z.number(),
	    rejectedPackageDraftCount: zod_1.z.number(),
	    rejectedNoValidatorCount: zod_1.z.number(),
	    rejectedParameterMissingCount: zod_1.z.number(),
	    rejectedParameterOutOfRangeCount: zod_1.z.number(),
	    rejectedSignalsNotMappedCount: zod_1.z.number(),
	    rejectedOtherCount: zod_1.z.number(),
	}));
	
	return bomResolutionEvents;
}

var wizardEvents = {};

var hasRequiredWizardEvents;

function requireWizardEvents () {
	if (hasRequiredWizardEvents) return wizardEvents;
	hasRequiredWizardEvents = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.WizardFragmentFailedEvent = exports.WizardFragmentCreatedEvent = exports.WizardPlanAssignedEvent = exports.WizardReviewReplicaSetCancelledEvent = exports.WizardReviewReplicaSetFailedEvent = exports.WizardReviewReplicaSetCompletedEvent = exports.WizardReviewLifecycleFailedEvent = exports.WizardReviewCancelledEvent = exports.WizardReviewFailedEvent = exports.WizardReviewCompletedEvent = exports.WizardReviewCreatedEvent = exports.WizardReviewReplicaSetCreatedEvent = exports.WizardPlanCancelledEvent = exports.planCancellationReasonSchema = exports.PlanCancellationReason = exports.WizardPlanDiscardedEvent = exports.WizardPlanCompletedEvent = exports.WizardStepAvailableEvent = exports.WizardPlanCreatedEvent = exports.WizardFragmentCancelledEvent = exports.WizardFragmentCompletedEvent = exports.WizardClaimReleasedEvent = exports.WizardClaimExpiredEvent = exports.WizardClaimProlongedEvent = exports.WizardStepAbortedEvent = exports.WizardStepSubmittedEvent = exports.WizardStepReopenedEvent = exports.WizardStepClaimedEvent = exports.FragmentFailureCauses = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const event_1 = requireEvent();
		exports.FragmentFailureCauses = ['operatorAbort', 'finalizerError', 'rollup'];
		exports.WizardStepClaimedEvent = (0, event_1.createEventClass)('WizardStepClaimedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		    stepType: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		    assignedUserPoolId: zod_1.z.string().nullable().optional(),
		    priorityBucket: zod_1.z.string().optional(),
		}));
		exports.WizardStepReopenedEvent = (0, event_1.createEventClass)('WizardStepReopenedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string().optional(),
		    reopenedStepIds: zod_1.z.array(zod_1.z.string()),
		    isTraineeMode: zod_1.z.boolean(),
		}));
		exports.WizardStepSubmittedEvent = (0, event_1.createEventClass)('WizardStepSubmittedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		    stepType: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string().optional(),
		    durationMs: zod_1.z.number().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		    assignedUserPoolId: zod_1.z.string().nullable().optional(),
		    priorityBucket: zod_1.z.string().optional(),
		}));
		exports.WizardStepAbortedEvent = (0, event_1.createEventClass)('WizardStepAbortedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		    stepType: zod_1.z.string(),
		    fragmentId: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string().optional(),
		    reason: zod_1.z.string(),
		    isFailureAbort: zod_1.z.boolean(),
		    isTraineeMode: zod_1.z.boolean(),
		    assignedUserPoolId: zod_1.z.string().nullable().optional(),
		    priorityBucket: zod_1.z.string().optional(),
		}));
		exports.WizardClaimProlongedEvent = (0, event_1.createEventClass)('WizardClaimProlongedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		}));
		exports.WizardClaimExpiredEvent = (0, event_1.createEventClass)('WizardClaimExpiredEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		    expiredAt: zod_1.z.string(),
		}));
		exports.WizardClaimReleasedEvent = (0, event_1.createEventClass)('WizardClaimReleasedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    stepId: zod_1.z.string(),
		}));
		exports.WizardFragmentCompletedEvent = (0, event_1.createEventClass)('WizardFragmentCompletedEvent', zod_1.z.object({
		    userId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    generatorName: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		    generatorId: zod_1.z.string().optional(),
		    assignedUserPoolId: zod_1.z.string().nullable().optional(),
		    durationMs: zod_1.z.number().optional(),
		}));
		exports.WizardFragmentCancelledEvent = (0, event_1.createEventClass)('WizardFragmentCancelledEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    fragmentName: zod_1.z.string(),
		    reason: zod_1.z.string(),
		    cancelledStepCount: zod_1.z.number(),
		    generatorId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string().optional(),
		    assignedUserPoolId: zod_1.z.string().nullable().optional(),
		    packageId: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean().optional(),
		}));
		exports.WizardPlanCreatedEvent = (0, event_1.createEventClass)('WizardPlanCreatedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    generatorName: zod_1.z.string(),
		    stepCount: zod_1.z.number(),
		    assignedToOrgId: zod_1.z.string().nullable(),
		    assignedToUserId: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		    spawnPurpose: zod_1.z.string().optional(),
		    inferredSigmaModelType: zod_1.z.string().optional(),
		}));
		exports.WizardStepAvailableEvent = (0, event_1.createEventClass)('WizardStepAvailableEvent', zod_1.z.object({
		    stepId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    stepType: zod_1.z.string(),
		    fragmentId: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		    assignedUserPoolId: zod_1.z.string().nullable().optional(),
		    priorityBucket: zod_1.z.string().optional(),
		}));
		exports.WizardPlanCompletedEvent = (0, event_1.createEventClass)('WizardPlanCompletedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    generatorName: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    totalDurationMs: zod_1.z.number().optional(),
		    stepCount: zod_1.z.number(),
		    fragmentCount: zod_1.z.number(),
		    userId: zod_1.z.string(),
		    isTraineeMode: zod_1.z.boolean(),
		}));
		exports.WizardPlanDiscardedEvent = (0, event_1.createEventClass)('WizardPlanDiscardedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    packageId: zod_1.z.string(),
		    reason: zod_1.z.enum(['staleResolution', 'packagePublished']),
		    planCreatedAtMs: zod_1.z.number(),
		    latestResolutionAtMs: zod_1.z.number().optional(),
		    generatorName: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    userId: zod_1.z.string().optional(),
		}));
		var PlanCancellationReason;
		(function (PlanCancellationReason) {
		    PlanCancellationReason["AdminCancel"] = "adminCancel";
		    PlanCancellationReason["ReviewerCancel"] = "reviewerCancel";
		    PlanCancellationReason["StepFailure"] = "stepFailure";
		    PlanCancellationReason["Prune"] = "prune";
		})(PlanCancellationReason || (exports.PlanCancellationReason = PlanCancellationReason = {}));
		exports.planCancellationReasonSchema = zod_1.z.nativeEnum(PlanCancellationReason);
		exports.WizardPlanCancelledEvent = (0, event_1.createEventClass)('WizardPlanCancelledEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    generatorName: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		    orgId: zod_1.z.string().optional(),
		    cancellationReason: exports.planCancellationReasonSchema,
		    triggeredByUserId: zod_1.z.string().optional(),
		    note: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		}));
		exports.WizardReviewReplicaSetCreatedEvent = (0, event_1.createEventClass)('WizardReviewReplicaSetCreatedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    replicaSetId: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    reviewCount: zod_1.z.number(),
		    manufacturerPartNumber: zod_1.z.string().optional(),
		    sigmaModelType: zod_1.z.string().optional(),
		    pinCount: zod_1.z.number().int().optional(),
		}));
		exports.WizardReviewCreatedEvent = (0, event_1.createEventClass)('WizardReviewCreatedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    reviewId: zod_1.z.string(),
		    ref: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    manufacturerPartNumber: zod_1.z.string().optional(),
		    sigmaModelType: zod_1.z.string().optional(),
		    pinCount: zod_1.z.number().int().optional(),
		}));
		const reviewTerminalFields = {
		    planId: zod_1.z.string(),
		    reviewId: zod_1.z.string(),
		    ref: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string(),
		    isTraineeMode: zod_1.z.boolean(),
		    durationMs: zod_1.z.number().optional(),
		    completedByUserIds: zod_1.z.array(zod_1.z.string()),
		    didWriteToPackageStore: zod_1.z.boolean(),
		    blobPath: zod_1.z.string().nullable(),
		    manufacturerPartNumber: zod_1.z.string().optional(),
		    sigmaModelType: zod_1.z.string().optional(),
		    pinCount: zod_1.z.number().int().optional(),
		};
		exports.WizardReviewCompletedEvent = (0, event_1.createEventClass)('WizardReviewCompletedEvent', zod_1.z.object(reviewTerminalFields));
		exports.WizardReviewFailedEvent = (0, event_1.createEventClass)('WizardReviewFailedEvent', zod_1.z.object(reviewTerminalFields));
		exports.WizardReviewCancelledEvent = (0, event_1.createEventClass)('WizardReviewCancelledEvent', zod_1.z.object(reviewTerminalFields));
		exports.WizardReviewLifecycleFailedEvent = (0, event_1.createEventClass)('WizardReviewLifecycleFailedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    phase: zod_1.z.enum(['creation', 'rollup']),
		    error: zod_1.z.string(),
		}));
		const replicaSetTerminalFields = {
		    planId: zod_1.z.string(),
		    replicaSetId: zod_1.z.string(),
		    packageId: zod_1.z.string().optional(),
		    generatorName: zod_1.z.string(),
		    isTraineeMode: zod_1.z.boolean(),
		    durationMs: zod_1.z.number().optional(),
		    reviewCount: zod_1.z.number(),
		    manufacturerPartNumber: zod_1.z.string().optional(),
		    sigmaModelType: zod_1.z.string().optional(),
		    pinCount: zod_1.z.number().int().optional(),
		};
		exports.WizardReviewReplicaSetCompletedEvent = (0, event_1.createEventClass)('WizardReviewReplicaSetCompletedEvent', zod_1.z.object(replicaSetTerminalFields));
		exports.WizardReviewReplicaSetFailedEvent = (0, event_1.createEventClass)('WizardReviewReplicaSetFailedEvent', zod_1.z.object(replicaSetTerminalFields));
		exports.WizardReviewReplicaSetCancelledEvent = (0, event_1.createEventClass)('WizardReviewReplicaSetCancelledEvent', zod_1.z.object(replicaSetTerminalFields));
		exports.WizardPlanAssignedEvent = (0, event_1.createEventClass)('WizardPlanAssignedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    generatorName: zod_1.z.string().optional(),
		    packageId: zod_1.z.string().optional(),
		    previousOrgId: zod_1.z.string().nullable(),
		    previousUserId: zod_1.z.string().nullable(),
		    assignedToOrgId: zod_1.z.string().nullable(),
		    assignedToUserId: zod_1.z.string().nullable(),
		    isTraineeMode: zod_1.z.boolean(),
		}));
		exports.WizardFragmentCreatedEvent = (0, event_1.createEventClass)('WizardFragmentCreatedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    generatorId: zod_1.z.string(),
		    generatorName: zod_1.z.string(),
		    assignedUserPoolId: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().optional(),
		    priorityBucket: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		}));
		exports.WizardFragmentFailedEvent = (0, event_1.createEventClass)('WizardFragmentFailedEvent', zod_1.z.object({
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    generatorId: zod_1.z.string(),
		    generatorName: zod_1.z.string().optional(),
		    completedByUserId: zod_1.z.string().nullable(),
		    assignedUserPoolId: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().optional(),
		    failureCause: zod_1.z.enum(exports.FragmentFailureCauses),
		    failureReason: zod_1.z.string().optional(),
		    note: zod_1.z.string().optional(),
		    isTraineeMode: zod_1.z.boolean(),
		}));
		
	} (wizardEvents));
	return wizardEvents;
}

var reviewPipelineEvents = {};

var reviewTerminalStatus = {};

var hasRequiredReviewTerminalStatus;

function requireReviewTerminalStatus () {
	if (hasRequiredReviewTerminalStatus) return reviewTerminalStatus;
	hasRequiredReviewTerminalStatus = 1;
	Object.defineProperty(reviewTerminalStatus, "__esModule", { value: true });
	reviewTerminalStatus.ReviewTerminalStatuses = void 0;
	reviewTerminalStatus.ReviewTerminalStatuses = ['complete', 'failed', 'cancelled'];
	
	return reviewTerminalStatus;
}

var hasRequiredReviewPipelineEvents;

function requireReviewPipelineEvents () {
	if (hasRequiredReviewPipelineEvents) return reviewPipelineEvents;
	hasRequiredReviewPipelineEvents = 1;
	Object.defineProperty(reviewPipelineEvents, "__esModule", { value: true });
	reviewPipelineEvents.PackageReviewCompletedEvent = reviewPipelineEvents.PackageReviewsRequestedEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const review_terminal_status_1 = requireReviewTerminalStatus();
	reviewPipelineEvents.PackageReviewsRequestedEvent = (0, event_1.createEventClass)('PackageReviewsRequestedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    manufacturerPartNumber: zod_1.z.string(),
	    sigmaModelType: zod_1.z.string(),
	    pinCount: zod_1.z.number().int(),
	    planId: zod_1.z.string(),
	    reviewCount: zod_1.z.number().int(),
	}));
	reviewPipelineEvents.PackageReviewCompletedEvent = (0, event_1.createEventClass)('PackageReviewCompletedEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    planId: zod_1.z.string(),
	    reviewId: zod_1.z.string(),
	    ref: zod_1.z.string(),
	    outcome: zod_1.z.enum(review_terminal_status_1.ReviewTerminalStatuses),
	    completedByUserId: zod_1.z.string().optional(),
	    pinCount: zod_1.z.number().int(),
	}));
	
	return reviewPipelineEvents;
}

var exceptionReviewEvents = {};

var hasRequiredExceptionReviewEvents;

function requireExceptionReviewEvents () {
	if (hasRequiredExceptionReviewEvents) return exceptionReviewEvents;
	hasRequiredExceptionReviewEvents = 1;
	Object.defineProperty(exceptionReviewEvents, "__esModule", { value: true });
	exceptionReviewEvents.ExceptionReviewCancelledEvent = exceptionReviewEvents.ExceptionReviewSkippedEvent = exceptionReviewEvents.ExceptionReviewCompleted = exceptionReviewEvents.ExceptionReviewOutputResolutionWritten = exceptionReviewEvents.ExceptionReviewSubmissionAttemptedEvent = exceptionReviewEvents.ExceptionReviewOpenedEvent = exceptionReviewEvents.ExceptionReviewReadyEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	const identity = {
	    packageId: zod_1.z.string(),
	    manufacturerPartNumber: zod_1.z.string(),
	    pinCount: zod_1.z.number().int().optional(),
	    planId: zod_1.z.string(),
	};
	exceptionReviewEvents.ExceptionReviewReadyEvent = (0, event_1.createEventClass)('ExceptionReviewReadyEvent', zod_1.z.object({ ...identity, pooledReviewCount: zod_1.z.number().int().optional() }));
	exceptionReviewEvents.ExceptionReviewOpenedEvent = (0, event_1.createEventClass)('ExceptionReviewOpenedEvent', zod_1.z.object({ ...identity, userId: zod_1.z.string() }));
	exceptionReviewEvents.ExceptionReviewSubmissionAttemptedEvent = (0, event_1.createEventClass)('ExceptionReviewSubmissionAttemptedEvent', zod_1.z.object({ ...identity, userId: zod_1.z.string() }));
	exceptionReviewEvents.ExceptionReviewOutputResolutionWritten = (0, event_1.createEventClass)('ExceptionReviewOutputResolutionWritten', zod_1.z.object({ ...identity, userId: zod_1.z.string(), packageStatus: zod_1.z.enum(['published', 'unpublished']) }));
	exceptionReviewEvents.ExceptionReviewCompleted = (0, event_1.createEventClass)('ExceptionReviewCompleted', zod_1.z.object({ ...identity, userId: zod_1.z.string() }));
	exceptionReviewEvents.ExceptionReviewSkippedEvent = (0, event_1.createEventClass)('ExceptionReviewSkippedEvent', zod_1.z.object({ ...identity, userId: zod_1.z.string() }));
	exceptionReviewEvents.ExceptionReviewCancelledEvent = (0, event_1.createEventClass)('ExceptionReviewCancelledEvent', zod_1.z.object({ ...identity, userId: zod_1.z.string(), reason: zod_1.z.string(), note: zod_1.z.string().optional() }));
	
	return exceptionReviewEvents;
}

var consensusEvents = {};

var hasRequiredConsensusEvents;

function requireConsensusEvents () {
	if (hasRequiredConsensusEvents) return consensusEvents;
	hasRequiredConsensusEvents = 1;
	Object.defineProperty(consensusEvents, "__esModule", { value: true });
	consensusEvents.ConsensusRunDeadEndedEvent = consensusEvents.ConsensusDeadEndReason = consensusEvents.ConsensusDeadEndStatus = consensusEvents.ConsensusAutoAcceptResolutionWrittenEvent = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const event_1 = requireEvent();
	consensusEvents.ConsensusAutoAcceptResolutionWrittenEvent = (0, event_1.createEventClass)('ConsensusAutoAcceptResolutionWrittenEvent', zod_1.z.object({
	    packageId: zod_1.z.string(),
	    manufacturerPartNumber: zod_1.z.string(),
	    pinCount: zod_1.z.number().int(),
	    planId: zod_1.z.string(),
	    consensusRunId: zod_1.z.string(),
	}));
	var ConsensusDeadEndStatus;
	(function (ConsensusDeadEndStatus) {
	    ConsensusDeadEndStatus["Ineligible"] = "ineligible";
	    ConsensusDeadEndStatus["RecordedShadow"] = "recorded_shadow";
	})(ConsensusDeadEndStatus || (consensusEvents.ConsensusDeadEndStatus = ConsensusDeadEndStatus = {}));
	var ConsensusDeadEndReason;
	(function (ConsensusDeadEndReason) {
	    ConsensusDeadEndReason["NoSubmissions"] = "no_submissions";
	    ConsensusDeadEndReason["AutoAcceptWriteDisabled"] = "auto_accept_write_disabled";
	    ConsensusDeadEndReason["SpawnDisabled"] = "spawn_disabled";
	})(ConsensusDeadEndReason || (consensusEvents.ConsensusDeadEndReason = ConsensusDeadEndReason = {}));
	consensusEvents.ConsensusRunDeadEndedEvent = (0, event_1.createEventClass)('ConsensusRunDeadEndedEvent', zod_1.z.object({
	    packageId: zod_1.z.string().nullable(),
	    planId: zod_1.z.string(),
	    status: zod_1.z.nativeEnum(ConsensusDeadEndStatus),
	    trigger: zod_1.z.string(),
	    submissionCount: zod_1.z.number().int().nonnegative().optional(),
	    reason: zod_1.z.nativeEnum(ConsensusDeadEndReason),
	}));
	
	return consensusEvents;
}

var hasRequiredEvents;

function requireEvents () {
	if (hasRequiredEvents) return Events;
	hasRequiredEvents = 1;
	(function (exports) {
		var __createBinding = (Events && Events.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Events && Events.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePackageEvents(), exports);
		__exportStar(requirePackageMilestoneEnums(), exports);
		__exportStar(requireIngestionEvents(), exports);
		__exportStar(requirePackageUnderstandingEvents(), exports);
		__exportStar(requireAgenticExtractionEvents(), exports);
		__exportStar(requirePackageDataAuditEvents(), exports);
		__exportStar(requireCustomerBomMergeEvents(), exports);
		__exportStar(requireCustomerBomMergeEnums(), exports);
		__exportStar(requireFastModeEvents(), exports);
		__exportStar(requireBuildLifecycleEvents(), exports);
		__exportStar(requireBuildLifecycleEnums(), exports);
		__exportStar(requireAnalysisEvents(), exports);
		__exportStar(requireBomResolutionEnums(), exports);
		__exportStar(requireBomResolutionEvents(), exports);
		__exportStar(requireWizardEvents(), exports);
		__exportStar(requireReviewPipelineEvents(), exports);
		__exportStar(requireExceptionReviewEvents(), exports);
		__exportStar(requireConsensusEvents(), exports);
		__exportStar(requireEvent(), exports);
		
	} (Events));
	return Events;
}

var ExpressionExpansion = {};

var expressionTokens = {};

var hasRequiredExpressionTokens;

function requireExpressionTokens () {
	if (hasRequiredExpressionTokens) return expressionTokens;
	hasRequiredExpressionTokens = 1;
	Object.defineProperty(expressionTokens, "__esModule", { value: true });
	expressionTokens.comparisonWithEquals = comparisonWithEquals;
	expressionTokens.isSignOperator = isSignOperator;
	expressionTokens.operatorForCharacter = operatorForCharacter;
	expressionTokens.isNumberDraft = isNumberDraft;
	expressionTokens.resolveWord = resolveWord;
	expressionTokens.getSuggestions = getSuggestions;
	expressionTokens.ingestExpressionText = ingestExpressionText;
	expressionTokens.expressionToText = expressionToText;
	expressionTokens.validateExpression = validateExpression;
	const OPERATOR_DEFINITIONS = [
	    { symbol: '×', name: 'multiplication', aliases: ['×', 'x', '*'] },
	    { symbol: '/', name: 'division', aliases: ['/', '÷'] },
	    { symbol: '+', name: 'addition', aliases: ['+'] },
	    { symbol: '−', name: 'subtraction', aliases: ['-', '−'] },
	    { symbol: '≤', name: 'at most', aliases: ['≤'] },
	    { symbol: '≥', name: 'at least', aliases: ['≥'] },
	    { symbol: '<', name: 'less than', aliases: ['<'] },
	    { symbol: '>', name: 'greater than', aliases: ['>'] },
	    { symbol: '(', name: 'open parenthesis', aliases: ['('] },
	    { symbol: ')', name: 'close parenthesis', aliases: [')'] },
	];
	const UNIT_DEFINITIONS = [
	    { symbol: 'V', name: 'volts' },
	    { symbol: 'mV', name: 'millivolts' },
	    { symbol: 'µV', name: 'microvolts' },
	    { symbol: 'A', name: 'amps' },
	    { symbol: 'mA', name: 'milliamps' },
	    { symbol: 'µA', name: 'microamps' },
	    { symbol: 'nA', name: 'nanoamps' },
	    { symbol: 'W', name: 'watts' },
	    { symbol: 'mW', name: 'milliwatts' },
	    { symbol: 'µW', name: 'microwatts' },
	    { symbol: 'Ω', name: 'ohms' },
	    { symbol: 'kΩ', name: 'kilohms' },
	    { symbol: 'MΩ', name: 'megohms' },
	    { symbol: 'Hz', name: 'hertz' },
	    { symbol: 'kHz', name: 'kilohertz' },
	    { symbol: 'MHz', name: 'megahertz' },
	    { symbol: 's', name: 'seconds' },
	    { symbol: 'ms', name: 'milliseconds' },
	    { symbol: 'µs', name: 'microseconds' },
	    { symbol: 'ns', name: 'nanoseconds' },
	    { symbol: '°C', name: 'degrees Celsius' },
	    { symbol: 'pF', name: 'picofarads' },
	    { symbol: 'nF', name: 'nanofarads' },
	    { symbol: 'µF', name: 'microfarads' },
	];
	const FUNCTION_DEFINITIONS = ['Min', 'Max', 'Abs', 'Round', 'Floor', 'Ceil'];
	const SEPARATOR_CHARACTER = ',';
	const NUMBER_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)$/;
	const DRAFT_CHARACTER = /[\p{L}\p{N}_.°µΩ]/u;
	function makeOperatorToken(definition) {
	    return { kind: 'operator', symbol: definition.symbol, name: definition.name };
	}
	function makeUnitToken(definition) {
	    return { kind: 'unit', symbol: definition.symbol, name: definition.name };
	}
	function isParen(token, side) {
	    return token?.kind === 'operator' && token.symbol === side;
	}
	const COMPARISON_SYMBOLS = ['<', '>', '≤', '≥'];
	const COMPARISON_ALIASES = [
	    ['<=', '≤'],
	    ['>=', '≥'],
	];
	function comparisonWithEquals(token) {
	    if (token?.kind !== 'operator') {
	        return null;
	    }
	    const upgraded = token.symbol === '<' ? '≤'
	        : token.symbol === '>' ? '≥'
	            : null;
	    return upgraded === null ? null : operatorForCharacter(upgraded);
	}
	function isComparison(token) {
	    return token?.kind === 'operator' && COMPARISON_SYMBOLS.includes(token.symbol);
	}
	function isSignOperator(tokens, index) {
	    const token = tokens[index];
	    if (token?.kind !== 'operator' || (token.symbol !== '+' && token.symbol !== '−')) {
	        return false;
	    }
	    if (tokens[index + 1]?.kind !== 'number') {
	        return false;
	    }
	    const previous = tokens[index - 1];
	    return (previous === undefined || previous.kind === 'separator' || (previous.kind === 'operator' && !isParen(previous, ')')));
	}
	function operatorForCharacter(character) {
	    const definition = OPERATOR_DEFINITIONS.find((candidate) => candidate.aliases.includes(character.toLowerCase()));
	    return definition === undefined ? null : makeOperatorToken(definition);
	}
	function isNumberDraft(draft) {
	    return NUMBER_PATTERN.test(draft);
	}
	function resolveWord(word, signals) {
	    if (isNumberDraft(word) && !word.endsWith('.')) {
	        return { kind: 'number', text: word };
	    }
	    if (word.length === 1) {
	        const operator = operatorForCharacter(word);
	        if (operator !== null) {
	            return operator;
	        }
	    }
	    const fn = FUNCTION_DEFINITIONS.find((name) => name.toLowerCase() === word.toLowerCase());
	    if (fn !== undefined) {
	        return { kind: 'function', name: fn };
	    }
	    const signal = signals.find((candidate) => candidate.name.toLowerCase() === word.toLowerCase());
	    if (signal !== undefined) {
	        return { kind: 'signal', signal };
	    }
	    const unit = UNIT_DEFINITIONS.find((candidate) => candidate.symbol.toLowerCase() === word.toLowerCase());
	    if (unit !== undefined) {
	        return makeUnitToken(unit);
	    }
	    return null;
	}
	function getSuggestions(draft, signals, getSignalContext) {
	    const query = draft.trim();
	    if (query === '' || isNumberDraft(query)) {
	        return [];
	    }
	    const lower = query.toLowerCase();
	    const suggestions = [];
	    if (query.length === 1) {
	        const operator = operatorForCharacter(query);
	        if (operator !== null) {
	            suggestions.push({ display: query, context: operator.name, token: operator });
	        }
	    }
	    const functions = FUNCTION_DEFINITIONS.filter((name) => name.toLowerCase().startsWith(lower)).map((name) => ({ display: name, context: 'function', token: { kind: 'function', name } }));
	    const units = UNIT_DEFINITIONS.filter((unit) => unit.symbol.toLowerCase().startsWith(lower)).map((unit) => ({ display: unit.symbol, context: 'units', token: makeUnitToken(unit) }));
	    const matchedSignals = signals
	        .filter((signal) => signal.name.toLowerCase().startsWith(lower))
	        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
	        .map((signal) => ({
	        display: signal.name,
	        context: getSignalContext(signal),
	        token: { kind: 'signal', signal },
	    }));
	    suggestions.push(...functions, ...units, ...matchedSignals);
	    const exactRank = (suggestion) => {
	        if (suggestion.display.toLowerCase() !== lower) {
	            return 0;
	        }
	        return (suggestion.token.kind === 'operator' ? 4
	            : suggestion.token.kind === 'function' ? 3
	                : suggestion.token.kind === 'signal' ? 2
	                    : 1);
	    };
	    return suggestions.sort((a, b) => exactRank(b) - exactRank(a));
	}
	function ingestExpressionText(text, signals) {
	    const tokens = [];
	    let draft = '';
	    let error = null;
	    let folded = text;
	    for (const [alias, symbol] of COMPARISON_ALIASES) {
	        folded = folded.split(alias).join(symbol);
	    }
	    const commitDraft = () => {
	        if (draft === '') {
	            return true;
	        }
	        const token = resolveWord(draft, signals);
	        if (token === null) {
	            error = `"${draft}" isn't a known signal, function, unit, or operator.`;
	            return false;
	        }
	        tokens.push(token);
	        draft = '';
	        return true;
	    };
	    for (const character of folded) {
	        if (error !== null) {
	            break;
	        }
	        if (DRAFT_CHARACTER.test(character)) {
	            draft += character;
	            const split = /^(\d+(?:\.\d+)?|\.\d+)([\p{L}°].*)$/u.exec(draft);
	            const numberPart = split?.[1];
	            const rest = split?.[2];
	            const prefixesSignal = signals.some((signal) => signal.name.toLowerCase().startsWith(draft.toLowerCase()));
	            if (numberPart !== undefined && rest !== undefined && !prefixesSignal) {
	                tokens.push({ kind: 'number', text: numberPart });
	                draft = rest;
	            }
	            continue;
	        }
	        if (character === ' ') {
	            commitDraft();
	            continue;
	        }
	        if (character === SEPARATOR_CHARACTER) {
	            if (commitDraft()) {
	                tokens.push({ kind: 'separator' });
	            }
	            continue;
	        }
	        const operator = operatorForCharacter(character);
	        if (operator !== null) {
	            if (commitDraft()) {
	                tokens.push(operator);
	            }
	            continue;
	        }
	        error = `"${character}" isn't a supported operator.`;
	    }
	    return { tokens, draft, error };
	}
	function tokenText(token) {
	    switch (token.kind) {
	        case 'signal': {
	            return token.signal.name;
	        }
	        case 'number': {
	            return token.text;
	        }
	        case 'function': {
	            return token.name;
	        }
	        case 'separator': {
	            return SEPARATOR_CHARACTER;
	        }
	        default: {
	            return token.symbol;
	        }
	    }
	}
	function expressionToText(tokens) {
	    let text = '';
	    let previous = null;
	    for (const [index, token] of tokens.entries()) {
	        const noSpaceBefore = isParen(token, ')') || token.kind === 'separator' || (token.kind === 'unit' && previous?.kind === 'number');
	        const noSpaceAfterPrevious = previous !== null &&
	            (isParen(previous, '(') || previous.kind === 'function' || isSignOperator(tokens, index - 1));
	        text += text === '' || noSpaceBefore || noSpaceAfterPrevious ? tokenText(token) : ` ${tokenText(token)}`;
	        previous = token;
	    }
	    return text;
	}
	function validateExpression(tokens) {
	    const parenIsFunctionCall = [];
	    let expectOperand = true;
	    let comparisons = 0;
	    for (const [index, token] of tokens.entries()) {
	        const next = tokens[index + 1];
	        const previous = tokens[index - 1];
	        if (token.kind === 'function' && !isParen(next, '(')) {
	            return `"${token.name}" must be followed by "(".`;
	        }
	        const openParen = isParen(token, '(');
	        const closeParen = isParen(token, ')');
	        const binaryOperator = token.kind === 'operator' && !openParen && !closeParen;
	        if (token.kind === 'unit') {
	            if (previous?.kind !== 'number') {
	                return `"${token.symbol}" must follow a number.`;
	            }
	        }
	        else if (binaryOperator) {
	            if (isComparison(token)) {
	                comparisons += 1;
	                if (comparisons > 1) {
	                    return 'A value can state only one comparison.';
	                }
	                if (parenIsFunctionCall.length > 0) {
	                    return `"${token.symbol}" compares the whole value, so it can't sit inside parentheses.`;
	                }
	            }
	            if (expectOperand) {
	                if (isSignOperator(tokens, index)) ;
	                else if (isComparison(token) && previous === undefined) ;
	                else {
	                    return previous === undefined ?
	                        `An expression can't start with "${token.symbol}".`
	                        : `"${token.symbol}" needs a value before it.`;
	                }
	            }
	            else {
	                expectOperand = true;
	            }
	        }
	        else if (closeParen) {
	            if (expectOperand) {
	                return 'A value is missing before ")".';
	            }
	        }
	        else if (token.kind === 'separator') {
	            if (expectOperand) {
	                return 'A comma needs an argument before it.';
	            }
	            expectOperand = true;
	        }
	        else {
	            if (!expectOperand) {
	                return `"${tokenText(token)}" needs an operator before it.`;
	            }
	            expectOperand = openParen || token.kind === 'function';
	        }
	        if (openParen) {
	            parenIsFunctionCall.push(previous?.kind === 'function');
	            if (isParen(next, ')')) {
	                return "Parentheses can't be empty.";
	            }
	        }
	        if (closeParen) {
	            if (parenIsFunctionCall.length === 0) {
	                return 'Unmatched ")".';
	            }
	            parenIsFunctionCall.pop();
	            expectOperand = false;
	        }
	        if (token.kind === 'separator') {
	            if (parenIsFunctionCall[parenIsFunctionCall.length - 1] !== true) {
	                return "A comma can only separate arguments inside a function's parentheses.";
	            }
	            if (previous === undefined || previous.kind === 'separator' || isParen(previous, '(')) {
	                return 'A comma needs an argument before it.';
	            }
	            if (next === undefined || next.kind === 'separator' || isParen(next, ')')) {
	                return 'A comma needs an argument after it.';
	            }
	        }
	    }
	    if (parenIsFunctionCall.length > 0) {
	        return 'Unclosed "(".';
	    }
	    const last = tokens[tokens.length - 1];
	    if (expectOperand && last !== undefined) {
	        return last.kind === 'operator' ? `"${last.symbol}" needs a value after it.` : 'The expression is incomplete.';
	    }
	    return null;
	}
	
	return expressionTokens;
}

var ratingExpansion_types = {};

var hasRequiredRatingExpansion_types;

function requireRatingExpansion_types () {
	if (hasRequiredRatingExpansion_types) return ratingExpansion_types;
	hasRequiredRatingExpansion_types = 1;
	Object.defineProperty(ratingExpansion_types, "__esModule", { value: true });
	ratingExpansion_types.ExpansionStatus = ratingExpansion_types.BoundOverrideOp = ratingExpansion_types.RatingRowOrigin = ratingExpansion_types.RatingsRelativeReferenceKind = ratingExpansion_types.RatingsKind = void 0;
	ratingExpansion_types.kindDefaultLevel = kindDefaultLevel;
	const sigma_engine_shared_1 = requireDist$1();
	var RatingsKind;
	(function (RatingsKind) {
	    RatingsKind["Amr"] = "amr";
	    RatingsKind["Oc"] = "oc";
	})(RatingsKind || (ratingExpansion_types.RatingsKind = RatingsKind = {}));
	function kindDefaultLevel(kind) {
	    switch (kind) {
	        case RatingsKind.Amr: {
	            return sigma_engine_shared_1.BoundaryLevel.Error;
	        }
	        case RatingsKind.Oc: {
	            return sigma_engine_shared_1.BoundaryLevel.Warning;
	        }
	    }
	}
	var RatingsRelativeReferenceKind;
	(function (RatingsRelativeReferenceKind) {
	    RatingsRelativeReferenceKind["Signal"] = "signal";
	    RatingsRelativeReferenceKind["Mapping"] = "mapping";
	})(RatingsRelativeReferenceKind || (ratingExpansion_types.RatingsRelativeReferenceKind = RatingsRelativeReferenceKind = {}));
	var RatingRowOrigin;
	(function (RatingRowOrigin) {
	    RatingRowOrigin["Entered"] = "entered";
	    RatingRowOrigin["Reconstructed"] = "reconstructed";
	})(RatingRowOrigin || (ratingExpansion_types.RatingRowOrigin = RatingRowOrigin = {}));
	var BoundOverrideOp;
	(function (BoundOverrideOp) {
	    BoundOverrideOp["Add"] = "add";
	    BoundOverrideOp["Remove"] = "remove";
	    BoundOverrideOp["Change"] = "change";
	})(BoundOverrideOp || (ratingExpansion_types.BoundOverrideOp = BoundOverrideOp = {}));
	var ExpansionStatus;
	(function (ExpansionStatus) {
	    ExpansionStatus["Expanded"] = "expanded";
	    ExpansionStatus["Unrecognized"] = "unrecognized";
	})(ExpansionStatus || (ratingExpansion_types.ExpansionStatus = ExpansionStatus = {}));
	
	return ratingExpansion_types;
}

var ratingExpansionEngine = {};

var hasRequiredRatingExpansionEngine;

function requireRatingExpansionEngine () {
	if (hasRequiredRatingExpansionEngine) return ratingExpansionEngine;
	hasRequiredRatingExpansionEngine = 1;
	Object.defineProperty(ratingExpansionEngine, "__esModule", { value: true });
	ratingExpansionEngine.RatingExpansionEngine = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const expression_tokens_1 = requireExpressionTokens();
	const rating_expansion_types_1 = requireRatingExpansion_types();
	class Refusal {
	    constructor(reason) {
	        this.reason = reason;
	    }
	}
	const UNRECOGNIZED = "This expression form isn't recognized for automatic expansion.";
	const SLOT_LABEL = {
	    min: 'Minimum',
	    typ: 'Typical',
	    max: 'Maximum',
	};
	const SLOT_ORDER = ['min', 'typ', 'max'];
	const COMPARISON_SYMBOLS = new Set(['<', '>', '≤', '≥']);
	const NUMERICAL_TYPE_ORDER = ['range', 'minimum', 'maximum', 'absolute'];
	class RatingExpansionEngine {
	    static expand(row, context) {
	        if (context.kind === rating_expansion_types_1.RatingsKind.Amr && row.typ.trim() !== '') {
	            return RatingExpansionEngine.refuse('An absolute maximum rating has no typical value — clear the typical cell.');
	        }
	        const targetSets = RatingExpansionEngine.targetSets(row, context);
	        if (targetSets instanceof Refusal) {
	            return RatingExpansionEngine.refuse(targetSets.reason);
	        }
	        const conditions = RatingExpansionEngine.conditions(row, context);
	        if (conditions instanceof Refusal) {
	            return RatingExpansionEngine.refuse(conditions.reason);
	        }
	        const expanded = [];
	        for (const slot of SLOT_ORDER) {
	            const text = row[slot].trim();
	            if (text === '') {
	                continue;
	            }
	            const result = RatingExpansionEngine.expandSlot(slot, text, row, context);
	            if (result instanceof Refusal) {
	                return RatingExpansionEngine.refuse(`${SLOT_LABEL[slot]}: ${result.reason}`);
	            }
	            expanded.push(result);
	        }
	        RatingExpansionEngine.correctReversedRange(expanded);
	        const limitSets = new Map();
	        for (const targetSet of targetSets) {
	            for (const slot of expanded) {
	                for (const term of slot.terms) {
	                    const relativeSignalId = term.signalId ?? targetSet.relativeSignalId;
	                    if (relativeSignalId === null && row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
	                        return RatingExpansionEngine.refuse(`${SLOT_LABEL[slot.slot]}: a voltage stated as a plain number needs the row's "measured from" reference.`);
	                    }
	                    const key = `${JSON.stringify(targetSet.target)}|${relativeSignalId ?? ''}`;
	                    const limitSet = limitSets.get(key) ?? {
	                        target: targetSet.target,
	                        relativeSignalId,
	                        floors: [],
	                        ceilings: [],
	                        typicals: [],
	                    };
	                    limitSets.set(key, limitSet);
	                    const flip = targetSet.inverting && term.signalId === null;
	                    const signed = flip ? -term.value : term.value;
	                    const limit = { value: signed === 0 ? 0 : signed, inclusive: slot.inclusive !== false };
	                    const isFloor = flip ? slot.slot === 'max' : slot.slot === 'min';
	                    const isCeiling = flip ? slot.slot === 'min' : slot.slot === 'max';
	                    (isFloor ? limitSet.floors
	                        : isCeiling ? limitSet.ceilings
	                            : limitSet.typicals).push(limit);
	                }
	            }
	        }
	        const bounds = [];
	        for (const limitSet of limitSets.values()) {
	            const numericals = RatingExpansionEngine.fold(limitSet, row.characteristic);
	            if (numericals instanceof Refusal) {
	                return RatingExpansionEngine.refuse(numericals.reason);
	            }
	            for (const numerical of numericals) {
	                bounds.push(RatingExpansionEngine.bound(row, context, limitSet, numerical, conditions));
	            }
	        }
	        const unique = [...new Map(bounds.map((bound) => [bound.id, bound])).values()];
	        return { status: rating_expansion_types_1.ExpansionStatus.Expanded, bounds: RatingExpansionEngine.sort(unique), reason: null };
	    }
	    static refuse(reason) {
	        return { status: rating_expansion_types_1.ExpansionStatus.Unrecognized, bounds: [], reason };
	    }
	    static targetSets(row, context) {
	        if (row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Power) {
	            if (row.relative !== null) {
	                return new Refusal('A power rating is a whole-device limit — it takes no reference.');
	            }
	            return [{ target: { component: true }, relativeSignalId: null, inverting: false }];
	        }
	        const members = [...(row.signalIds ?? [])];
	        for (const groupId of row.groupIds) {
	            const group = context.signalGroups.find((candidate) => candidate.id === groupId);
	            if (group === undefined) {
	                return new Refusal(`The row applies to a signal group the table doesn't declare (${groupId}).`);
	            }
	            members.push(...group.signalIds);
	        }
	        const targets = [...new Set(members)].sort();
	        if (targets.length === 0) {
	            return new Refusal('The row applies to no signals.');
	        }
	        for (const targetId of targets) {
	            if (!RatingExpansionEngine.hasSignal(context, targetId)) {
	                return new Refusal(`The row applies to a signal the package doesn't declare (${targetId}).`);
	            }
	        }
	        if (row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Current) {
	            if (row.relative !== null) {
	                return new Refusal('A current rating is a plain number — it takes no reference.');
	            }
	            return targets.map((targetId) => ({ target: { signal: targetId }, relativeSignalId: null, inverting: false }));
	        }
	        if (row.relative === null || row.relative.kind === rating_expansion_types_1.RatingsRelativeReferenceKind.Signal) {
	            const relativeSignalId = row.relative?.signalId ?? null;
	            if (relativeSignalId !== null && !RatingExpansionEngine.hasSignal(context, relativeSignalId)) {
	                return new Refusal(`The row's reference names a signal the package doesn't declare (${relativeSignalId}).`);
	            }
	            return targets.map((targetId) => ({ target: { signal: targetId }, relativeSignalId, inverting: false }));
	        }
	        const pairs = new Map(row.relative.pairs.map((pair) => [pair.targetId, pair.relativeId]));
	        for (const pair of row.relative.pairs) {
	            if (!targets.includes(pair.targetId)) {
	                return new Refusal(`The reference mapping names a signal the row doesn't apply to (${pair.targetId}).`);
	            }
	        }
	        const sets = [];
	        for (const targetId of targets) {
	            const relativeId = pairs.get(targetId);
	            if (relativeId === undefined || relativeId === null) {
	                return new Refusal(`The reference mapping gives ${targetId} nothing to be measured from.`);
	            }
	            if (!RatingExpansionEngine.hasSignal(context, relativeId)) {
	                return new Refusal(`The reference mapping names a signal the package doesn't declare (${relativeId}).`);
	            }
	            const mutual = pairs.get(relativeId) === targetId;
	            const pair = context.pairs.find((candidate) => (candidate.positiveId === targetId && candidate.negativeId === relativeId) ||
	                (candidate.positiveId === relativeId && candidate.negativeId === targetId));
	            if (mutual && pair === undefined) {
	                return new Refusal(`${targetId} and ${relativeId} are measured from each other but aren't declared as a differential pair.`);
	            }
	            sets.push({
	                target: { signal: targetId },
	                relativeSignalId: relativeId,
	                inverting: mutual && pair !== undefined && pair.negativeId === targetId,
	            });
	        }
	        return sets;
	    }
	    static conditions(row, context) {
	        const ids = [...new Set(row.conditionIds)].sort();
	        for (const id of ids) {
	            if (!context.conditions.some((condition) => condition.id === id)) {
	                return new Refusal(`The row holds under a condition the table doesn't declare (${id}).`);
	            }
	        }
	        return ids.map((mode) => ({ mode, mode_state: true }));
	    }
	    static hasSignal(context, id) {
	        return context.signals.some((signal) => String(signal.id) === id);
	    }
	    static fold(limitSet, characteristic) {
	        const byValue = (a, b) => a.value - b.value;
	        const [floor, ...moreFloors] = [...limitSet.floors].sort(byValue);
	        const [ceiling, ...moreCeilings] = [...limitSet.ceilings].sort(byValue);
	        const [typical, ...moreTypicals] = [...limitSet.typicals].sort(byValue);
	        const nominal = typical === undefined ? {} : { nominalValue: typical.value };
	        const numericals = [];
	        if (floor !== undefined && ceiling !== undefined) {
	            if (floor.value > ceiling.value) {
	                return new Refusal(`The minimum (${floor.value}) is above the maximum (${ceiling.value}) against the same reference.`);
	            }
	            numericals.push({
	                type: 'range',
	                characteristic,
	                minVal: floor.value,
	                maxVal: ceiling.value,
	                ...(floor.inclusive ? {} : { minValIncluded: false }),
	                ...(ceiling.inclusive ? {} : { maxValIncluded: false }),
	                ...nominal,
	            });
	        }
	        else if (floor !== undefined) {
	            numericals.push({
	                type: 'minimum',
	                characteristic,
	                value: floor.value,
	                ...(floor.inclusive ? {} : { valInclusive: false }),
	                ...nominal,
	            });
	        }
	        else if (ceiling !== undefined) {
	            numericals.push({
	                type: 'maximum',
	                characteristic,
	                value: ceiling.value,
	                ...(ceiling.inclusive ? {} : { valInclusive: false }),
	                ...nominal,
	            });
	        }
	        else if (typical !== undefined) {
	            numericals.push({ type: 'absolute', characteristic, value: typical.value });
	        }
	        for (const extra of moreFloors) {
	            numericals.push({
	                type: 'minimum',
	                characteristic,
	                value: extra.value,
	                ...(extra.inclusive ? {} : { valInclusive: false }),
	            });
	        }
	        for (const extra of moreCeilings) {
	            numericals.push({
	                type: 'maximum',
	                characteristic,
	                value: extra.value,
	                ...(extra.inclusive ? {} : { valInclusive: false }),
	            });
	        }
	        for (const extra of moreTypicals) {
	            numericals.push({ type: 'absolute', characteristic, value: extra.value });
	        }
	        return numericals;
	    }
	    static bound(row, context, limitSet, numerical, conditions) {
	        const amr = context.kind === rating_expansion_types_1.RatingsKind.Amr;
	        const content = {
	            ...(conditions.length === 0 ? { global: true } : { conditions }),
	            boundary: {
	                type: sigma_engine_shared_1.BoundaryAssertionType.Numerical,
	                class: amr ? sigma_engine_shared_1.BoundaryClass.IC_AbsoluteMaximumRating : sigma_engine_shared_1.BoundaryClass.IC_ElectricalCharacteristics,
	                level: (0, rating_expansion_types_1.kindDefaultLevel)(context.kind),
	                target: limitSet.target,
	                ...(limitSet.relativeSignalId === null ? {} : { relative_to: { signal: limitSet.relativeSignalId } }),
	                numerical,
	            },
	        };
	        const prefix = amr ? 'amr' : 'iec';
	        const identity = `${row.id}|${RatingExpansionEngine.canonicalJson(content)}`;
	        const id = `${prefix}_${sigma_engine_shared_1.Utils.fnv1aHash(identity)}${sigma_engine_shared_1.Utils.fnv1aHash(`${identity}|salt`)}`;
	        return { id, ...content };
	    }
	    static canonicalJson(value) {
	        if (Array.isArray(value)) {
	            return `[${value.map((item) => RatingExpansionEngine.canonicalJson(item)).join(',')}]`;
	        }
	        if (value !== null && typeof value === 'object') {
	            const entries = Object.entries(value)
	                .filter(([, item]) => item !== undefined)
	                .sort(([a], [b]) => (a < b ? -1 : 1))
	                .map(([key, item]) => `${JSON.stringify(key)}:${RatingExpansionEngine.canonicalJson(item)}`);
	            return `{${entries.join(',')}}`;
	        }
	        return JSON.stringify(value);
	    }
	    static sort(bounds) {
	        const key = (bound) => {
	            const { boundary } = bound;
	            const numerical = boundary.type === sigma_engine_shared_1.BoundaryAssertionType.Numerical ? boundary.numerical : null;
	            const relative = boundary.relative_to;
	            const reference = relative !== undefined && 'signal' in relative && typeof relative.signal === 'string' ? relative.signal : '';
	            return [
	                JSON.stringify(boundary.target),
	                reference,
	                numerical === null ? -1 : NUMERICAL_TYPE_ORDER.indexOf(numerical.type),
	                numerical === null ? 0
	                    : numerical.type === 'range' ? numerical.minVal
	                        : numerical.value,
	            ];
	        };
	        return [...bounds].sort((a, b) => {
	            const [targetA, referenceA, typeA, valueA] = key(a);
	            const [targetB, referenceB, typeB, valueB] = key(b);
	            if (targetA !== targetB) {
	                return targetA < targetB ? -1 : 1;
	            }
	            if (referenceA !== referenceB) {
	                return referenceA < referenceB ? -1 : 1;
	            }
	            if (typeA !== typeB) {
	                return typeA - typeB;
	            }
	            return valueA - valueB;
	        });
	    }
	    static expandSlot(slot, text, row, context) {
	        const ingested = (0, expression_tokens_1.ingestExpressionText)(`${text} `, RatingExpansionEngine.resolvableSignals(context));
	        if (ingested.error !== null) {
	            return new Refusal(ingested.error);
	        }
	        if (ingested.draft !== '') {
	            return new Refusal(`"${ingested.draft}" isn't a complete signal, function, unit, or number.`);
	        }
	        const structural = (0, expression_tokens_1.validateExpression)(ingested.tokens);
	        if (structural !== null) {
	            return new Refusal(structural);
	        }
	        let tokens = RatingExpansionEngine.foldSigns(ingested.tokens);
	        if (tokens.some((token) => token.kind === 'operator' && (token.symbol === '×' || token.symbol === '/'))) {
	            return new Refusal("Multiplicative limits can't be held as an offset from a reference, and products aren't supported. " +
	                'Evaluate it against the nominal rail voltage and enter the result (0.5 × VCC at a nominal 5 V is 2.5 V).');
	        }
	        let inclusive;
	        let statesDirection = false;
	        const [subject, comparison] = tokens;
	        if (subject?.kind === 'signal' &&
	            comparison?.kind === 'operator' &&
	            COMPARISON_SYMBOLS.has(comparison.symbol) &&
	            RatingExpansionEngine.isTarget(row, context, String(subject.signal.id))) {
	            tokens = tokens.slice(1);
	        }
	        const [first] = tokens;
	        if (first?.kind === 'operator' && COMPARISON_SYMBOLS.has(first.symbol)) {
	            if (slot === 'typ') {
	                return new Refusal("A typical value can't state a comparison.");
	            }
	            const impliedSlot = first.symbol === '<' || first.symbol === '≤' ? 'max' : 'min';
	            if (impliedSlot !== slot) {
	                const limit = impliedSlot === 'max' ? 'an upper' : 'a lower';
	                const cell = impliedSlot === 'max' ? 'maximum' : 'minimum';
	                return new Refusal(`"${first.symbol}" states ${limit} limit, so it belongs in the ${cell} cell.`);
	            }
	            inclusive = first.symbol === '≤' || first.symbol === '≥' ? undefined : false;
	            statesDirection = true;
	            tokens = tokens.slice(1);
	        }
	        if (tokens.some((token) => token.kind === 'operator' && COMPARISON_SYMBOLS.has(token.symbol))) {
	            return new Refusal(UNRECOGNIZED);
	        }
	        const [head] = tokens;
	        if (head?.kind !== 'function') {
	            const term = RatingExpansionEngine.parseTerm(tokens, row.characteristic);
	            if (term instanceof Refusal) {
	                return term;
	            }
	            if (term === null) {
	                return new Refusal(UNRECOGNIZED);
	            }
	            const plain = head?.kind === 'number' && tokens.length <= 2 && !statesDirection;
	            return { slot, terms: [term], plain, ...(inclusive === undefined ? {} : { inclusive }) };
	        }
	        if (head.name !== 'Min' && head.name !== 'Max') {
	            return new Refusal('Only Min() and Max() composites can be expanded.');
	        }
	        if (slot === 'typ') {
	            return new Refusal("A typical value can't hold a Min()/Max() composite.");
	        }
	        if ((head.name === 'Min') !== (slot === 'max')) {
	            const boundName = slot === 'min' ? 'a lower' : 'an upper';
	            return new Refusal(`${head.name}() on ${boundName} bound is a disjunction — it can't be split into simultaneous bounds.`);
	        }
	        const terms = RatingExpansionEngine.parseComposite(tokens, head.name, row.characteristic);
	        if (terms instanceof Refusal) {
	            return terms;
	        }
	        return { slot, terms, plain: false, ...(inclusive === undefined ? {} : { inclusive }) };
	    }
	    static resolvableSignals(context) {
	        const aliases = context.signals.map((signal) => ({ id: signal.id, name: `V${signal.name}` }));
	        return [...context.signals, ...aliases];
	    }
	    static isTarget(row, context, signalId) {
	        return ((row.signalIds ?? []).includes(signalId) ||
	            context.signalGroups.some((group) => row.groupIds.includes(group.id) && group.signalIds.includes(signalId)));
	    }
	    static parseComposite(tokens, name, characteristic) {
	        const [, second] = tokens;
	        if (!RatingExpansionEngine.isParen(second, '(') || !RatingExpansionEngine.isParen(tokens[tokens.length - 1], ')')) {
	            return new Refusal(UNRECOGNIZED);
	        }
	        let depth = 0;
	        for (const [index, token] of tokens.entries()) {
	            if (RatingExpansionEngine.isParen(token, '(')) {
	                depth += 1;
	            }
	            if (RatingExpansionEngine.isParen(token, ')')) {
	                depth -= 1;
	                if (depth === 0 && index !== tokens.length - 1) {
	                    return new Refusal(UNRECOGNIZED);
	                }
	            }
	        }
	        const terms = [];
	        for (const argument of RatingExpansionEngine.splitTopLevelArgs(tokens.slice(2, -1))) {
	            const parsed = RatingExpansionEngine.parseArgument(argument, name, characteristic);
	            if (parsed instanceof Refusal) {
	                return parsed;
	            }
	            terms.push(...parsed);
	        }
	        return terms;
	    }
	    static parseArgument(tokens, outerName, characteristic) {
	        const simple = RatingExpansionEngine.parseTerm(tokens, characteristic);
	        if (simple instanceof Refusal) {
	            return simple;
	        }
	        if (simple !== null) {
	            return [simple];
	        }
	        const [first, second] = tokens;
	        if (first?.kind !== 'function') {
	            return new Refusal(UNRECOGNIZED);
	        }
	        if (first.name !== outerName) {
	            return new Refusal(first.name === 'Min' || first.name === 'Max' ?
	                `${first.name}() nested inside ${outerName}() flips direction — expand it manually.`
	                : 'Only Min() and Max() composites can be expanded.');
	        }
	        if (!RatingExpansionEngine.isParen(second, '(')) {
	            return new Refusal(UNRECOGNIZED);
	        }
	        let depth = 0;
	        let closeIndex = -1;
	        for (const [index, token] of tokens.entries()) {
	            if (RatingExpansionEngine.isParen(token, '(')) {
	                depth += 1;
	            }
	            if (RatingExpansionEngine.isParen(token, ')')) {
	                depth -= 1;
	                if (depth === 0) {
	                    closeIndex = index;
	                    break;
	                }
	            }
	        }
	        if (closeIndex === -1) {
	            return new Refusal(UNRECOGNIZED);
	        }
	        const trailing = tokens.slice(closeIndex + 1);
	        let offset = null;
	        if (trailing.length > 0) {
	            const [operator, number, unit] = trailing;
	            const shapeIsOffset = operator?.kind === 'operator' &&
	                (operator.symbol === '+' || operator.symbol === '−') &&
	                number?.kind === 'number' &&
	                trailing.length <= 3 &&
	                (unit === undefined || unit.kind === 'unit');
	            if (!shapeIsOffset) {
	                return new Refusal(UNRECOGNIZED);
	            }
	            const magnitude = RatingExpansionEngine.toBaseUnit(number, unit?.kind === 'unit' ? unit : undefined, characteristic);
	            if (magnitude instanceof Refusal) {
	                return magnitude;
	            }
	            offset = operator.symbol === '−' ? -magnitude : magnitude;
	        }
	        const terms = [];
	        for (const inner of RatingExpansionEngine.splitTopLevelArgs(tokens.slice(2, closeIndex))) {
	            const parsed = RatingExpansionEngine.parseArgument(inner, outerName, characteristic);
	            if (parsed instanceof Refusal) {
	                return parsed;
	            }
	            for (const term of parsed) {
	                if (offset === null) {
	                    terms.push(term);
	                    continue;
	                }
	                if (term.signalId === null || term.value !== 0) {
	                    return new Refusal("Offsets can't be combined across nesting levels — expand this composite manually.");
	                }
	                terms.push({ value: offset, signalId: term.signalId });
	            }
	        }
	        return terms;
	    }
	    static parseTerm(tokens, characteristic) {
	        const [first, second, third, fourth] = tokens;
	        if (first?.kind === 'number' && tokens.length <= 2) {
	            if (second !== undefined && second.kind !== 'unit') {
	                return null;
	            }
	            const value = RatingExpansionEngine.toBaseUnit(first, second, characteristic);
	            return value instanceof Refusal ? value : { value, signalId: null };
	        }
	        if (first?.kind !== 'signal') {
	            return null;
	        }
	        if (characteristic !== sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
	            return new Refusal(`A ${characteristic} rating is a plain number — it can't be measured relative to "${first.signal.name}".`);
	        }
	        const signalId = String(first.signal.id);
	        if (tokens.length === 1) {
	            return { value: 0, signalId };
	        }
	        if (second?.kind !== 'operator' || (second.symbol !== '+' && second.symbol !== '−') || third?.kind !== 'number') {
	            return null;
	        }
	        if (tokens.length > 4 || (fourth !== undefined && fourth.kind !== 'unit')) {
	            return null;
	        }
	        const magnitude = RatingExpansionEngine.toBaseUnit(third, fourth, characteristic);
	        if (magnitude instanceof Refusal) {
	            return magnitude;
	        }
	        return { value: second.symbol === '−' ? -magnitude : magnitude, signalId };
	    }
	    static toBaseUnit(number, unit, characteristic) {
	        const declaration = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
	        if (unit !== undefined && !unit.symbol.endsWith(declaration.short)) {
	            return new Refusal(`"${unit.symbol}" isn't a unit of ${characteristic}.`);
	        }
	        const text = `${number.text}${unit?.symbol ?? ''}`;
	        try {
	            return (0, sigma_engine_shared_1.parseNumericalSIValue)(text, declaration);
	        }
	        catch {
	            return new Refusal(`"${text}" couldn't be read as a number of ${declaration.long}.`);
	        }
	    }
	    static foldSigns(tokens) {
	        const folded = [];
	        for (const [index, token] of tokens.entries()) {
	            if ((0, expression_tokens_1.isSignOperator)(tokens, index)) {
	                continue;
	            }
	            const previous = tokens[index - 1];
	            if (token.kind === 'number' && previous?.kind === 'operator' && (0, expression_tokens_1.isSignOperator)(tokens, index - 1)) {
	                folded.push({ kind: 'number', text: `${previous.symbol === '−' ? '-' : ''}${token.text}` });
	                continue;
	            }
	            folded.push(token);
	        }
	        return folded;
	    }
	    static correctReversedRange(expanded) {
	        const min = expanded.find((slot) => slot.slot === 'min');
	        const max = expanded.find((slot) => slot.slot === 'max');
	        if (min === undefined || max === undefined || !min.plain || !max.plain) {
	            return;
	        }
	        const [minTerm] = min.terms;
	        const [maxTerm] = max.terms;
	        if (minTerm === undefined || maxTerm === undefined || minTerm.value <= maxTerm.value) {
	            return;
	        }
	        min.terms = [maxTerm];
	        max.terms = [minTerm];
	    }
	    static splitTopLevelArgs(tokens) {
	        const args = [];
	        let current = [];
	        let depth = 0;
	        for (const token of tokens) {
	            if (RatingExpansionEngine.isParen(token, '(')) {
	                depth += 1;
	            }
	            if (RatingExpansionEngine.isParen(token, ')')) {
	                depth -= 1;
	            }
	            if (token.kind === 'separator' && depth === 0) {
	                args.push(current);
	                current = [];
	                continue;
	            }
	            current.push(token);
	        }
	        args.push(current);
	        return args;
	    }
	    static isParen(token, side) {
	        return token?.kind === 'operator' && token.symbol === side;
	    }
	}
	ratingExpansionEngine.RatingExpansionEngine = RatingExpansionEngine;
	
	return ratingExpansionEngine;
}

var hasRequiredExpressionExpansion;

function requireExpressionExpansion () {
	if (hasRequiredExpressionExpansion) return ExpressionExpansion;
	hasRequiredExpressionExpansion = 1;
	(function (exports) {
		var __createBinding = (ExpressionExpansion && ExpressionExpansion.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (ExpressionExpansion && ExpressionExpansion.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireExpressionTokens(), exports);
		__exportStar(requireRatingExpansion_types(), exports);
		__exportStar(requireRatingExpansionEngine(), exports);
		
	} (ExpressionExpansion));
	return ExpressionExpansion;
}

var RatingsTable = {};

var ratingsTable_types = {};

var hasRequiredRatingsTable_types;

function requireRatingsTable_types () {
	if (hasRequiredRatingsTable_types) return ratingsTable_types;
	hasRequiredRatingsTable_types = 1;
	Object.defineProperty(ratingsTable_types, "__esModule", { value: true });
	ratingsTable_types.RatingsTableSaveOutcome = void 0;
	var RatingsTableSaveOutcome;
	(function (RatingsTableSaveOutcome) {
	    RatingsTableSaveOutcome["Saved"] = "saved";
	    RatingsTableSaveOutcome["Refused"] = "refused";
	})(RatingsTableSaveOutcome || (ratingsTable_types.RatingsTableSaveOutcome = RatingsTableSaveOutcome = {}));
	
	return ratingsTable_types;
}

var storedRatingsTable = {};

var hasRequiredStoredRatingsTable;

function requireStoredRatingsTable () {
	if (hasRequiredStoredRatingsTable) return storedRatingsTable;
	hasRequiredStoredRatingsTable = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.StoredRatingsTableSchema = exports.StoredRatingRowSchema = exports.BoundOverrideSchema = exports.BoundOverrideChangeSchema = exports.BoundOverrideRemoveSchema = exports.BoundOverrideAddSchema = exports.RatingsFormSignalGroupSchema = exports.RatingsRelativeReferenceSchema = exports.RatingsRelativeMappingReferenceSchema = exports.RatingsRelativeSignalReferenceSchema = exports.RatingsRelativeSignalPairSchema = exports.BoundSlotSchema = exports.MAX_ROW_BARE_SIGNALS = exports.RATINGS_TABLE_FORM_VERSION = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const z = /*@__PURE__*/ requireZod();
		const ExpressionExpansion_1 = requireExpressionExpansion();
		exports.RATINGS_TABLE_FORM_VERSION = 'normalized-ratings-form/v2';
		exports.MAX_ROW_BARE_SIGNALS = 3;
		exports.BoundSlotSchema = z.enum(['min', 'typ', 'max']);
		exports.RatingsRelativeSignalPairSchema = z.object({
		    targetId: z.string(),
		    relativeId: z.string().nullable(),
		});
		exports.RatingsRelativeSignalReferenceSchema = z.object({
		    kind: z.literal(ExpressionExpansion_1.RatingsRelativeReferenceKind.Signal),
		    signalId: z.string().nullable(),
		});
		exports.RatingsRelativeMappingReferenceSchema = z.object({
		    kind: z.literal(ExpressionExpansion_1.RatingsRelativeReferenceKind.Mapping),
		    pairs: exports.RatingsRelativeSignalPairSchema.array(),
		});
		exports.RatingsRelativeReferenceSchema = z.discriminatedUnion('kind', [
		    exports.RatingsRelativeSignalReferenceSchema,
		    exports.RatingsRelativeMappingReferenceSchema,
		]);
		exports.RatingsFormSignalGroupSchema = z.object({
		    id: z.string(),
		    name: z.string(),
		    signalIds: z.string().array(),
		});
		exports.BoundOverrideAddSchema = z.object({
		    op: z.literal(ExpressionExpansion_1.BoundOverrideOp.Add),
		    bound: sigma_engine_shared_1.ComponentBoundarySchema,
		});
		exports.BoundOverrideRemoveSchema = z.object({
		    op: z.literal(ExpressionExpansion_1.BoundOverrideOp.Remove),
		    id: z.string(),
		});
		exports.BoundOverrideChangeSchema = z.object({
		    op: z.literal(ExpressionExpansion_1.BoundOverrideOp.Change),
		    id: z.string(),
		    slot: exports.BoundSlotSchema.optional(),
		    value: z.number().optional(),
		    relativeSignalId: z.string().nullable().optional(),
		    signalIds: z.string().array().optional(),
		    inclusive: z.boolean().optional(),
		    level: sigma_engine_shared_1.BoundaryLevelSchema.optional(),
		});
		exports.BoundOverrideSchema = z.discriminatedUnion('op', [
		    exports.BoundOverrideAddSchema,
		    exports.BoundOverrideRemoveSchema,
		    exports.BoundOverrideChangeSchema,
		]);
		exports.StoredRatingRowSchema = z.object({
		    id: z.string(),
		    origin: z.nativeEnum(ExpressionExpansion_1.RatingRowOrigin),
		    characteristic: z.enum(sigma_engine_shared_1.RatedBoundaryCharacteristics),
		    description: z.string(),
		    min: z.string(),
		    typ: z.string(),
		    max: z.string(),
		    relative: exports.RatingsRelativeReferenceSchema.nullable(),
		    conditionIds: z.string().array(),
		    groupIds: z.string().array(),
		    signalIds: z.string().array().optional(),
		    boundaryIds: z.string().array(),
		    overrides: exports.BoundOverrideSchema.array(),
		});
		exports.StoredRatingsTableSchema = z.object({
		    formVersion: z.literal(exports.RATINGS_TABLE_FORM_VERSION),
		    kind: z.nativeEnum(ExpressionExpansion_1.RatingsKind),
		    signalGroups: exports.RatingsFormSignalGroupSchema.array(),
		    rows: exports.StoredRatingRowSchema.array(),
		});
		
	} (storedRatingsTable));
	return storedRatingsTable;
}

var ratingsTableCodec = {};

var operatingConditionModes = {};

var formLatest$1 = {};

var hasRequiredFormLatest$1;

function requireFormLatest$1 () {
	if (hasRequiredFormLatest$1) return formLatest$1;
	hasRequiredFormLatest$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST = exports.NormalizedSignalDriveModesFormStateCodec = exports.BoundNormalizedSignalDriveModesForm = exports.NormalizedSignalDriveModesFormDataLatestSchema = exports.NormalizedSignalDriveModesFormStateSchema = exports.FormSignalGroupSchema = exports.FormOperatingConditionSchema = exports.OperatingConditionValueSchema = exports.FormThresholdRowSchema = exports.OperatingConditionOperator = exports.ThresholdType = exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION = void 0;
		exports.payloadToNormalizedSignalDriveModesFormState = payloadToNormalizedSignalDriveModesFormState;
		exports.normalizedSignalDriveModesFormStateToPayload = normalizedSignalDriveModesFormStateToPayload;
		const sigma_engine_shared_1 = requireDist$1();
		const z = /*@__PURE__*/ requireZod();
		const condition_mode_denormalized_1 = requireConditionModeDenormalized();
		const operating_condition_modes_1 = requireOperatingConditionModes();
		exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION = 'normalized-signal-drive-modes-form/v1';
		var ThresholdType;
		(function (ThresholdType) {
		    ThresholdType["Vih"] = "vih";
		    ThresholdType["Vil"] = "vil";
		    ThresholdType["Voh"] = "voh";
		    ThresholdType["Vol"] = "vol";
		})(ThresholdType || (exports.ThresholdType = ThresholdType = {}));
		var OperatingConditionOperator;
		(function (OperatingConditionOperator) {
		    OperatingConditionOperator["Minimum"] = "minimum";
		    OperatingConditionOperator["Maximum"] = "maximum";
		    OperatingConditionOperator["Range"] = "range";
		    OperatingConditionOperator["Absolute"] = "absolute";
		})(OperatingConditionOperator || (exports.OperatingConditionOperator = OperatingConditionOperator = {}));
		exports.FormThresholdRowSchema = z.object({
		    id: z.string(),
		    type: z.nativeEnum(ThresholdType),
		    min: z.string(),
		    typ: z.string(),
		    max: z.string(),
		    groupIds: z.string().array(),
		    conditionIds: z.string().array(),
		    driveModes: z.nativeEnum(sigma_engine_shared_1.SignalOutputDriveType).array(),
		    variantKey: z.string().optional(),
		});
		exports.OperatingConditionValueSchema = z
		    .object({
		    targetSignalId: z.string().nullable(),
		    operator: z.nativeEnum(OperatingConditionOperator),
		    valueA: z.number(),
		    valueB: z.number(),
		    relativeSignalId: z.string().nullable(),
		})
		    .transform((value) => value.operator === OperatingConditionOperator.Range && value.valueA > value.valueB ?
		    { ...value, valueA: value.valueB, valueB: value.valueA }
		    : value);
		exports.FormOperatingConditionSchema = z.object({
		    id: z.string(),
		    value: exports.OperatingConditionValueSchema,
		    readOnly: z.boolean().optional(),
		});
		exports.FormSignalGroupSchema = z.object({
		    id: z.string(),
		    name: z.string(),
		    signalIds: z.string().array(),
		    driveModes: z.nativeEnum(sigma_engine_shared_1.SignalOutputDriveType).array(),
		});
		exports.NormalizedSignalDriveModesFormStateSchema = z.object({
		    formVersion: z.literal(exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION),
		    signalGroups: exports.FormSignalGroupSchema.array(),
		    conditions: exports.FormOperatingConditionSchema.array(),
		    thresholds: exports.FormThresholdRowSchema.array(),
		});
		exports.NormalizedSignalDriveModesFormDataLatestSchema = exports.NormalizedSignalDriveModesFormStateSchema;
		class BoundNormalizedSignalDriveModesForm {
		    constructor(seed) {
		        this.seed = seed;
		    }
		    toFormData(payload) {
		        return payloadToNormalizedSignalDriveModesFormState(payload);
		    }
		    toCanonical(formState) {
		        return normalizedSignalDriveModesFormStateToPayload(formState, this.seed);
		    }
		}
		exports.BoundNormalizedSignalDriveModesForm = BoundNormalizedSignalDriveModesForm;
		class NormalizedSignalDriveModesFormStateCodec {
		    constructor() {
		        this.version = exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION;
		        this.formDataSchema = exports.NormalizedSignalDriveModesFormStateSchema;
		    }
		    bind(seed) {
		        return new BoundNormalizedSignalDriveModesForm(seed);
		    }
		}
		exports.NormalizedSignalDriveModesFormStateCodec = NormalizedSignalDriveModesFormStateCodec;
		exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST = new NormalizedSignalDriveModesFormStateCodec();
		function payloadToNormalizedSignalDriveModesFormState(payload) {
		    const inScope = inScopeConditionModes(payload);
		    const inScopeConditionIds = new Set(inScope.map(({ mode }) => mode.id));
		    const clustersBySignature = new Map();
		    for (const signal of payload.signals) {
		        if ((signal.supportedDriveModes ?? []).length === 0) {
		            continue;
		        }
		        const signature = driveSignature(signal);
		        const cluster = clustersBySignature.get(signature);
		        if (cluster === undefined) {
		            clustersBySignature.set(signature, { signalIds: [String(signal.id)], representative: signal });
		        }
		        else {
		            cluster.signalIds.push(String(signal.id));
		        }
		    }
		    const orderedClusters = [...clustersBySignature.values()].sort((left, right) => [...left.signalIds]
		        .sort()
		        .join(',')
		        .localeCompare([...right.signalIds].sort().join(',')));
		    const nameBySignalId = new Map(payload.signals.map((signal) => [String(signal.id), signal.name]));
		    let positionalCounter = 0;
		    const groups = orderedClusters.map((cluster, index) => {
		        const signalIds = [...cluster.signalIds].sort();
		        const driveModes = outputDriveModesOf(cluster.representative);
		        const name = signalIds.length === 1 ?
		            (nameBySignalId.get(signalIds[0]) ?? signalIds[0])
		            : `Signal group ${(positionalCounter += 1)}`;
		        return { id: `sdm-group-${index}`, name, signalIds, driveModes };
		    });
		    const rowByKey = new Map();
		    groups.forEach((group, index) => {
		        for (const partial of thresholdRowsForSignature(orderedClusters[index].representative, group.driveModes, inScopeConditionIds)) {
		            const key = [
		                partial.type,
		                partial.min,
		                partial.max,
		                partial.conditionIds.join(','),
		                [...partial.driveModes].sort().join(','),
		                partial.variantKey ?? '',
		            ].join('|');
		            const existing = rowByKey.get(key);
		            if (existing === undefined) {
		                rowByKey.set(key, { id: `sdm-threshold-${rowByKey.size}`, ...partial, groupIds: [group.id] });
		            }
		            else {
		                existing.groupIds.push(group.id);
		            }
		        }
		    });
		    return {
		        formVersion: exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION,
		        signalGroups: groups,
		        conditions: buildConditionRows(inScope),
		        thresholds: [...rowByKey.values()],
		    };
		}
		function normalizedSignalDriveModesFormStateToPayload(formState, seed) {
		    assertFormStateReferentialIntegrity(formState, seed);
		    const nameBySignalId = new Map(seed.signals.map((signal) => [String(signal.id), signal.name]));
		    const seedInScope = inScopeConditionModes(seed);
		    const seedInScopeById = new Map(seedInScope.map(({ mode, value }) => [mode.id, { mode, value }]));
		    const conditionIdToModeId = new Map();
		    const rebuiltInScopeById = new Map();
		    for (const condition of formState.conditions) {
		        const targetId = condition.value.targetSignalId ?? '';
		        const targetName = nameBySignalId.get(targetId) ?? targetId;
		        const seeded = seedInScopeById.get(condition.id);
		        if (seeded !== undefined && operating_condition_modes_1.OperatingConditionModes.valuesEqual(seeded.value, condition.value)) {
		            conditionIdToModeId.set(condition.id, condition.id);
		            rebuiltInScopeById.set(condition.id, seeded.mode);
		            continue;
		        }
		        const mode = operating_condition_modes_1.OperatingConditionModes.toMode(condition.value, targetName);
		        conditionIdToModeId.set(condition.id, mode.id);
		        rebuiltInScopeById.set(mode.id, mode);
		    }
		    const groupById = new Map(formState.signalGroups.map((group) => [group.id, group]));
		    const supportedTypes = new Map();
		    const markSupported = (signalId, driveType) => {
		        const set = supportedTypes.get(signalId) ?? new Set();
		        set.add(driveType);
		        supportedTypes.set(signalId, set);
		    };
		    const groupedSignalIds = new Set();
		    for (const group of formState.signalGroups) {
		        for (const signalId of group.signalIds) {
		            groupedSignalIds.add(signalId);
		            for (const driveType of group.driveModes) {
		                markSupported(signalId, driveType);
		            }
		        }
		    }
		    const onInputThreshold = new Set();
		    for (const row of formState.thresholds) {
		        if (!isInputThresholdType(row.type)) {
		            continue;
		        }
		        for (const groupId of row.groupIds) {
		            for (const signalId of groupById.get(groupId)?.signalIds ?? []) {
		                onInputThreshold.add(signalId);
		            }
		        }
		    }
		    for (const signal of seed.signals) {
		        const signalId = String(signal.id);
		        if (!groupedSignalIds.has(signalId)) {
		            continue;
		        }
		        if (isInputLikeSignal(signal) || (signal.signalDirection === undefined && onInputThreshold.has(signalId))) {
		            markSupported(signalId, sigma_engine_shared_1.SignalInputDriveType.HighZ);
		        }
		    }
		    const originalParamValues = new Map();
		    for (const signal of seed.signals) {
		        for (const driveMode of signal.supportedDriveModes ?? []) {
		            const parameters = driveMode.parameters ?? {};
		            for (const [field, text] of voltageParamEntries(driveMode)) {
		                const raw = parameters[field];
		                if (raw !== undefined && raw !== null) {
		                    originalParamValues.set(`${String(signal.id)}|${String(driveMode.type)}|${field}|${text}`, raw);
		                }
		            }
		        }
		    }
		    const originalConditionNames = new Map();
		    for (const signal of seed.signals) {
		        for (const driveMode of signal.supportedDriveModes ?? []) {
		            for (const condition of driveMode.conditions ?? []) {
		                const mode = conditionModeIdOf(condition);
		                if (mode !== undefined && condition.name !== undefined) {
		                    originalConditionNames.set(mode, condition.name);
		                }
		            }
		        }
		    }
		    const seedInScopeIds = new Set(seedInScope.map(({ mode }) => mode.id));
		    const unrepresentedConditions = new Map();
		    for (const signal of seed.signals) {
		        const occurrenceBySignature = new Map();
		        for (const driveMode of signal.supportedDriveModes ?? []) {
		            const conditionIds = conditionModeIdsOf(driveMode).filter((id) => seedInScopeIds.has(id));
		            const signature = `${String(driveMode.type)}|${conditionIds.join(',')}`;
		            const occurrence = occurrenceBySignature.get(signature) ?? 0;
		            occurrenceBySignature.set(signature, occurrence + 1);
		            const unrepresented = (driveMode.conditions ?? []).filter((condition) => conditionModeIdOf(condition) === undefined);
		            if (unrepresented.length > 0) {
		                const variantKey = occurrence === 0 ? '' : String(occurrence);
		                unrepresentedConditions.set(`${String(signal.id)}|${String(driveMode.type)}|${conditionIds.join('|')}#${variantKey}`, unrepresented);
		            }
		        }
		    }
		    const accumulator = new Map();
		    const accumulatorFor = (signalId, driveType, conditionSetKey, conditionModeIds) => {
		        const byType = accumulator.get(signalId) ?? new Map();
		        const byConditionSet = byType.get(driveType) ?? new Map();
		        const entry = byConditionSet.get(conditionSetKey) ?? { params: {}, conditionModeIds, conditionSetKey };
		        byConditionSet.set(conditionSetKey, entry);
		        byType.set(driveType, byConditionSet);
		        accumulator.set(signalId, byType);
		        return entry;
		    };
		    for (const row of formState.thresholds) {
		        const { field, slot } = THRESHOLD_PARAM[row.type];
		        const value = slot === 'min' ? row.min : row.max;
		        const conditionModeIds = row.conditionIds.map((id) => conditionIdToModeId.get(id) ?? id).sort();
		        const conditionSetKey = `${conditionModeIds.join('|')}#${row.variantKey ?? ''}`;
		        for (const groupId of row.groupIds) {
		            const group = groupById.get(groupId);
		            if (group === undefined) {
		                continue;
		            }
		            const driveTypes = isInputThresholdType(row.type) ? [sigma_engine_shared_1.SignalInputDriveType.HighZ]
		                : row.driveModes.length > 0 ? group.driveModes.filter((mode) => row.driveModes.includes(mode))
		                    : group.driveModes;
		            for (const signalId of group.signalIds) {
		                for (const driveType of driveTypes) {
		                    const entry = accumulatorFor(signalId, driveType, conditionSetKey, conditionModeIds);
		                    if (value !== '') {
		                        entry.params[field] = originalParamValues.get(`${signalId}|${driveType}|${field}|${value}`) ?? value;
		                    }
		                }
		            }
		        }
		    }
		    const signals = seed.signals.map((signal) => {
		        const signalId = String(signal.id);
		        const driveTypes = supportedTypes.get(signalId);
		        if (driveTypes === undefined || driveTypes.size === 0) {
		            return { ...signal, supportedDriveModes: [] };
		        }
		        const supportedDriveModes = [];
		        for (const driveType of [...driveTypes].sort()) {
		            const entries = accumulator.get(signalId)?.get(driveType);
		            if (entries === undefined || entries.size === 0) {
		                supportedDriveModes.push({ type: driveType });
		                continue;
		            }
		            for (const entry of entries.values()) {
		                const driveMode = { type: driveType };
		                if (Object.keys(entry.params).length > 0) {
		                    driveMode.parameters = entry.params;
		                }
		                const conditions = entry.conditionModeIds.map((mode) => {
		                    const name = originalConditionNames.get(mode);
		                    return name === undefined ?
		                        { condition: { mode, mode_state: true } }
		                        : { name, condition: { mode, mode_state: true } };
		                });
		                conditions.push(...(unrepresentedConditions.get(`${signalId}|${driveType}|${entry.conditionSetKey}`) ?? []));
		                if (conditions.length > 0) {
		                    driveMode.conditions = conditions;
		                }
		                supportedDriveModes.push(driveMode);
		            }
		        }
		        return { ...signal, supportedDriveModes };
		    });
		    const supersededModeIds = new Set();
		    for (const [conditionId, modeId] of conditionIdToModeId) {
		        if (conditionId !== modeId && seedInScopeById.has(conditionId)) {
		            supersededModeIds.add(conditionId);
		        }
		    }
		    const modes = [];
		    const seenModeIds = new Set();
		    for (const mode of seed.modes) {
		        if (supersededModeIds.has(mode.id)) {
		            continue;
		        }
		        modes.push(mode);
		        seenModeIds.add(mode.id);
		    }
		    for (const [modeId, mode] of rebuiltInScopeById) {
		        if (!seenModeIds.has(modeId)) {
		            modes.push(mode);
		            seenModeIds.add(modeId);
		        }
		    }
		    return { signals, modes };
		}
		function assertFormStateReferentialIntegrity(formState, seed) {
		    const groupIds = new Set(formState.signalGroups.map((group) => group.id));
		    const conditionIds = new Set(formState.conditions.map((condition) => condition.id));
		    const seedSignalIds = new Set(seed.signals.map((signal) => String(signal.id)));
		    const violations = [];
		    for (const group of formState.signalGroups) {
		        for (const signalId of group.signalIds) {
		            if (!seedSignalIds.has(signalId)) {
		                violations.push(new Error(`group "${group.id}" references unknown signal "${signalId}"`));
		            }
		        }
		    }
		    for (const row of formState.thresholds) {
		        for (const groupId of row.groupIds) {
		            if (!groupIds.has(groupId)) {
		                violations.push(new Error(`threshold "${row.id}" references unknown group "${groupId}"`));
		            }
		        }
		        for (const conditionId of row.conditionIds) {
		            if (!conditionIds.has(conditionId)) {
		                violations.push(new Error(`threshold "${row.id}" references unknown condition "${conditionId}"`));
		            }
		        }
		    }
		    if (violations.length > 0) {
		        const summary = `NormalizedSignalDriveModesForm state has ${violations.length} dangling reference(s)`;
		        throw new AggregateError(violations, summary);
		    }
		}
		function inScopeConditionModes(payload) {
		    const referenced = new Set();
		    for (const signal of payload.signals) {
		        for (const driveMode of signal.supportedDriveModes ?? []) {
		            for (const modeId of conditionModeIdsOf(driveMode)) {
		                referenced.add(modeId);
		            }
		        }
		    }
		    const modeById = new Map(payload.modes.map((mode) => [mode.id, mode]));
		    const inScope = [];
		    for (const modeId of [...referenced].sort()) {
		        const mode = modeById.get(modeId);
		        if (mode === undefined) {
		            continue;
		        }
		        const [denormalized] = (0, condition_mode_denormalized_1.convertModeBaseToConditionModeDenormalized)(mode, payload.signals);
		        if (denormalized === undefined) {
		            continue;
		        }
		        const value = operating_condition_modes_1.OperatingConditionModes.toValue(denormalized);
		        if (value === null) {
		            continue;
		        }
		        inScope.push({ mode, value });
		    }
		    return inScope;
		}
		function buildConditionRows(inScope) {
		    return inScope.map(({ mode, value }) => ({ id: mode.id, value, readOnly: true }));
		}
		function thresholdRowsForSignature(signal, groupOutputModes, inScopeConditionIds) {
		    const clusters = new Map();
		    const occurrenceBySignature = new Map();
		    for (const driveMode of signal.supportedDriveModes ?? []) {
		        const conditionIds = conditionModeIdsOf(driveMode).filter((id) => inScopeConditionIds.has(id));
		        const signature = `${String(driveMode.type)}|${conditionIds.join(',')}`;
		        const occurrence = occurrenceBySignature.get(signature) ?? 0;
		        occurrenceBySignature.set(signature, occurrence + 1);
		        const variantKey = occurrence === 0 ? undefined : String(occurrence);
		        for (const [field, value] of voltageParamEntries(driveMode)) {
		            const type = FIELD_THRESHOLD[field];
		            const key = `${type}|${value}|${conditionIds.join(',')}|${variantKey ?? ''}`;
		            const cluster = clusters.get(key) ?? {
		                type,
		                value,
		                conditionIds,
		                driveTypes: new Set(),
		                ...(variantKey === undefined ? {} : { variantKey }),
		            };
		            cluster.driveTypes.add(String(driveMode.type));
		            clusters.set(key, cluster);
		        }
		    }
		    const groupOutputsSorted = [...groupOutputModes].sort();
		    return [...clusters.values()].map((cluster) => {
		        const { slot } = THRESHOLD_PARAM[cluster.type];
		        const clusterOutputs = [...cluster.driveTypes].filter((type) => OUTPUT_DRIVE_TYPES.has(type)).sort();
		        const coversAllOutputs = clusterOutputs.length === groupOutputsSorted.length &&
		            clusterOutputs.every((mode, index) => mode === groupOutputsSorted[index]);
		        const driveModes = isInputThresholdType(cluster.type) || coversAllOutputs ? [] : clusterOutputs;
		        return {
		            type: cluster.type,
		            min: slot === 'min' ? cluster.value : '',
		            typ: '',
		            max: slot === 'max' ? cluster.value : '',
		            conditionIds: [...cluster.conditionIds].sort(),
		            driveModes,
		            ...(cluster.variantKey === undefined ? {} : { variantKey: cluster.variantKey }),
		        };
		    });
		}
		function driveSignature(signal) {
		    const driveModes = (signal.supportedDriveModes ?? [])
		        .map((driveMode) => ({
		        type: String(driveMode.type),
		        params: voltageParamEntries(driveMode)
		            .map(([field, value]) => `${field}=${value}`)
		            .sort(),
		        conditions: conditionModeIdsOf(driveMode),
		    }))
		        .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
		    return JSON.stringify(driveModes);
		}
		function outputDriveModesOf(signal) {
		    const modes = new Set();
		    for (const driveMode of signal.supportedDriveModes ?? []) {
		        if (OUTPUT_DRIVE_TYPES.has(String(driveMode.type))) {
		            modes.add(driveMode.type);
		        }
		    }
		    return [...modes].sort();
		}
		function voltageParamEntries(driveMode) {
		    const parameters = driveMode.parameters ?? {};
		    const entries = [];
		    for (const field of VOLTAGE_FIELDS) {
		        const raw = parameters[field];
		        if (raw !== undefined && raw !== null) {
		            entries.push([field, String(raw)]);
		        }
		    }
		    return entries;
		}
		function conditionModeIdOf(condition) {
		    const inner = condition.condition;
		    if (typeof inner !== 'object' || inner === null || !('mode' in inner)) {
		        return undefined;
		    }
		    const { mode } = inner;
		    return typeof mode === 'string' ? mode : undefined;
		}
		function conditionModeIdsOf(driveMode) {
		    return (driveMode.conditions ?? [])
		        .map((condition) => conditionModeIdOf(condition))
		        .filter((mode) => typeof mode === 'string')
		        .sort();
		}
		function isInputThresholdType(type) {
		    return type === ThresholdType.Vih || type === ThresholdType.Vil;
		}
		function isInputLikeSignal(signal) {
		    return signal.signalDirection === sigma_engine_shared_1.SignalDirection.Input || signal.signalDirection === sigma_engine_shared_1.SignalDirection.BiDirectionalIO;
		}
		const VOLTAGE_FIELDS = [
		    'outputHighVoltage',
		    'outputLowVoltage',
		    'inputHighVoltage',
		    'inputLowVoltage',
		];
		const THRESHOLD_PARAM = {
		    [ThresholdType.Voh]: { field: 'outputHighVoltage', slot: 'min' },
		    [ThresholdType.Vol]: { field: 'outputLowVoltage', slot: 'max' },
		    [ThresholdType.Vih]: { field: 'inputHighVoltage', slot: 'min' },
		    [ThresholdType.Vil]: { field: 'inputLowVoltage', slot: 'max' },
		};
		const FIELD_THRESHOLD = {
		    outputHighVoltage: ThresholdType.Voh,
		    outputLowVoltage: ThresholdType.Vol,
		    inputHighVoltage: ThresholdType.Vih,
		    inputLowVoltage: ThresholdType.Vil,
		};
		const OUTPUT_DRIVE_TYPES = new Set(Object.values(sigma_engine_shared_1.SignalOutputDriveType));
		
	} (formLatest$1));
	return formLatest$1;
}

var hasRequiredOperatingConditionModes;

function requireOperatingConditionModes () {
	if (hasRequiredOperatingConditionModes) return operatingConditionModes;
	hasRequiredOperatingConditionModes = 1;
	Object.defineProperty(operatingConditionModes, "__esModule", { value: true });
	operatingConditionModes.OperatingConditionModes = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const condition_mode_denormalized_1 = requireConditionModeDenormalized();
	const generate_readable_id_1 = requireGenerateReadableId();
	const form_latest_1 = requireFormLatest$1();
	class OperatingConditionModes {
	    static fromMode(mode, signals) {
	        const [denormalized] = (0, condition_mode_denormalized_1.convertModeBaseToConditionModeDenormalized)(mode, signals, sigma_engine_shared_1.BoundaryCharacteristic.Voltage);
	        if (denormalized === undefined) {
	            return null;
	        }
	        const value = OperatingConditionModes.toValue(denormalized);
	        return value === null ? null : { id: mode.id, value, readOnly: true };
	    }
	    static toMode(value, targetName) {
	        const bounds = OperatingConditionModes.toBounds(value);
	        const denormalized = {
	            id: (0, generate_readable_id_1.generateReadableConditionModeId)(targetName, bounds.min, bounds.nominal, bounds.max),
	            characteristic: sigma_engine_shared_1.BoundaryCharacteristic.Voltage,
	            minVal: bounds.min,
	            nominalVal: bounds.nominal,
	            maxVal: bounds.max,
	            targetSignalId: value.targetSignalId ?? '',
	            relativeSignalId: value.relativeSignalId ?? '',
	        };
	        return (0, condition_mode_denormalized_1.convertConditionModeToModeBase)(denormalized);
	    }
	    static toValue(denormalized) {
	        const { minVal, nominalVal, maxVal, targetSignalId, relativeSignalId } = denormalized;
	        if (minVal !== null && maxVal !== null) {
	            return {
	                targetSignalId,
	                operator: form_latest_1.OperatingConditionOperator.Range,
	                valueA: minVal,
	                valueB: maxVal,
	                relativeSignalId,
	            };
	        }
	        if (minVal !== null) {
	            return {
	                targetSignalId,
	                operator: form_latest_1.OperatingConditionOperator.Minimum,
	                valueA: minVal,
	                valueB: 0,
	                relativeSignalId,
	            };
	        }
	        if (maxVal !== null) {
	            return {
	                targetSignalId,
	                operator: form_latest_1.OperatingConditionOperator.Maximum,
	                valueA: maxVal,
	                valueB: 0,
	                relativeSignalId,
	            };
	        }
	        if (nominalVal !== null) {
	            return {
	                targetSignalId,
	                operator: form_latest_1.OperatingConditionOperator.Absolute,
	                valueA: nominalVal,
	                valueB: 0,
	                relativeSignalId,
	            };
	        }
	        return null;
	    }
	    static toBounds(value) {
	        switch (value.operator) {
	            case form_latest_1.OperatingConditionOperator.Range:
	                return { min: Math.min(value.valueA, value.valueB), nominal: null, max: Math.max(value.valueA, value.valueB) };
	            case form_latest_1.OperatingConditionOperator.Minimum:
	                return { min: value.valueA, nominal: null, max: null };
	            case form_latest_1.OperatingConditionOperator.Maximum:
	                return { min: null, nominal: null, max: value.valueA };
	            case form_latest_1.OperatingConditionOperator.Absolute:
	                return { min: null, nominal: value.valueA, max: null };
	        }
	    }
	    static valuesEqual(left, right) {
	        return (left.targetSignalId === right.targetSignalId &&
	            left.operator === right.operator &&
	            left.valueA === right.valueA &&
	            left.valueB === right.valueB &&
	            left.relativeSignalId === right.relativeSignalId);
	    }
	}
	operatingConditionModes.OperatingConditionModes = OperatingConditionModes;
	
	return operatingConditionModes;
}

var boundaryFacts = {};

var hasRequiredBoundaryFacts;

function requireBoundaryFacts () {
	if (hasRequiredBoundaryFacts) return boundaryFacts;
	hasRequiredBoundaryFacts = 1;
	Object.defineProperty(boundaryFacts, "__esModule", { value: true });
	boundaryFacts.BoundaryFacts = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const ExpressionExpansion_1 = requireExpressionExpansion();
	const SLOT_ORDER = ['min', 'typ', 'max'];
	class BoundaryFacts {
	    static { this.RELATIVE_EPSILON = 1e-9; }
	    static fromBoundaries(bounds, signals) {
	        const facts = [];
	        for (const bound of bounds) {
	            const definition = bound.boundary;
	            if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
	                continue;
	            }
	            const { characteristic } = definition.numerical;
	            if (!BoundaryFacts.isRated(characteristic)) {
	                continue;
	            }
	            const [relative] = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([...signals], definition.relative_to);
	            const targets = 'component' in definition.target ?
	                [null]
	                : (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([...signals], definition.target).map(String);
	            for (const signalId of targets) {
	                for (const limit of BoundaryFacts.limits(definition.numerical)) {
	                    facts.push({
	                        signalId,
	                        characteristic,
	                        ...limit,
	                        relativeSignalId: relative === undefined ? null : String(relative),
	                        conditionsKey: BoundaryFacts.conditionsKey(bound),
	                        level: definition.level ?? null,
	                    });
	                }
	            }
	        }
	        return BoundaryFacts.sorted(facts);
	    }
	    static equal(a, b, signals) {
	        return BoundaryFacts.factsEqual(BoundaryFacts.fromBoundaries(a, signals), BoundaryFacts.fromBoundaries(b, signals));
	    }
	    static factsEqual(a, b) {
	        const left = BoundaryFacts.distinct(a);
	        const right = BoundaryFacts.distinct(b);
	        return (left.length === right.length && left.every((fact, index) => BoundaryFacts.sameFact(fact, right[index] ?? null)));
	    }
	    static apply(bounds, overrides) {
	        let patched = bounds.map((bound) => structuredClone(bound));
	        for (const override of overrides) {
	            switch (override.op) {
	                case ExpressionExpansion_1.BoundOverrideOp.Add: {
	                    patched.push(structuredClone(override.bound));
	                    break;
	                }
	                case ExpressionExpansion_1.BoundOverrideOp.Remove: {
	                    patched = patched.filter((bound) => bound.id !== override.id);
	                    break;
	                }
	                case ExpressionExpansion_1.BoundOverrideOp.Change: {
	                    patched = patched.map((bound) => (bound.id === override.id ? BoundaryFacts.change(bound, override) : bound));
	                    break;
	                }
	            }
	        }
	        return patched;
	    }
	    static diff(expected, actual, signals) {
	        const unpairedActual = [...actual];
	        const pairs = [];
	        const take = (predicate) => {
	            const index = unpairedActual.findIndex(predicate);
	            if (index === -1) {
	                return null;
	            }
	            const [taken] = unpairedActual.splice(index, 1);
	            return taken ?? null;
	        };
	        for (const bound of expected) {
	            pairs.push({ expected: bound, actual: take((candidate) => candidate.id === bound.id) });
	        }
	        for (const pair of pairs) {
	            if (pair.actual !== null) {
	                continue;
	            }
	            const shape = BoundaryFacts.shapeKey(pair.expected);
	            const placement = BoundaryFacts.placementKey(pair.expected);
	            pair.actual =
	                take((candidate) => BoundaryFacts.shapeKey(candidate) === shape && BoundaryFacts.placementKey(candidate) === placement) ?? take((candidate) => BoundaryFacts.shapeKey(candidate) === shape);
	        }
	        const overrides = [];
	        for (const pair of pairs) {
	            if (pair.actual === null) {
	                overrides.push({ op: ExpressionExpansion_1.BoundOverrideOp.Remove, id: pair.expected.id });
	                continue;
	            }
	            const changes = BoundaryFacts.changes(pair.expected, pair.actual);
	            if (BoundaryFacts.equal(BoundaryFacts.apply([pair.expected], changes), [pair.actual], signals)) {
	                overrides.push(...changes);
	                continue;
	            }
	            overrides.push({ op: ExpressionExpansion_1.BoundOverrideOp.Remove, id: pair.expected.id }, { op: ExpressionExpansion_1.BoundOverrideOp.Add, bound: pair.actual });
	        }
	        for (const bound of unpairedActual) {
	            overrides.push({ op: ExpressionExpansion_1.BoundOverrideOp.Add, bound });
	        }
	        return overrides;
	    }
	    static limits(numerical) {
	        const nominal = numerical.type !== 'absolute' && numerical.nominalValue !== undefined ?
	            [{ slot: 'typ', value: numerical.nominalValue, inclusive: true }]
	            : [];
	        switch (numerical.type) {
	            case 'minimum':
	                return [{ slot: 'min', value: numerical.value, inclusive: numerical.valInclusive ?? true }, ...nominal];
	            case 'maximum':
	                return [...nominal, { slot: 'max', value: numerical.value, inclusive: numerical.valInclusive ?? true }];
	            case 'range':
	                return [
	                    { slot: 'min', value: numerical.minVal, inclusive: numerical.minValIncluded ?? true },
	                    ...nominal,
	                    { slot: 'max', value: numerical.maxVal, inclusive: numerical.maxValIncluded ?? true },
	                ];
	            case 'absolute':
	                return [{ slot: 'typ', value: numerical.value, inclusive: true }];
	        }
	    }
	    static conditionsKey(bound) {
	        return (bound.conditions ?? [])
	            .map((condition) => {
	            if (sigma_engine_shared_1.DesignModels.isModeReferenceCondition(condition)) {
	                const mode = BoundaryFacts.escapeKeyEntry(condition.mode);
	                return condition.mode_state ? mode : `!${mode}`;
	            }
	            return BoundaryFacts.escapeKeyEntry(JSON.stringify(condition));
	        })
	            .sort()
	            .join(',');
	    }
	    static escapeKeyEntry(entry) {
	        return entry.replace(/%/g, '%25').replace(/,/g, '%2C').replace(/!/g, '%21');
	    }
	    static change(bound, override) {
	        const definition = bound.boundary;
	        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
	            return bound;
	        }
	        const numerical = override.slot === undefined ?
	            definition.numerical
	            : BoundaryFacts.changeSlot(definition.numerical, override.slot, override.value, override.inclusive);
	        const { relative_to: _relative, ...withoutRelative } = definition;
	        const relative = override.relativeSignalId === undefined ? definition.relative_to
	            : override.relativeSignalId === null ? undefined
	                : { signal: override.relativeSignalId };
	        const [onlySignalId, ...moreSignalIds] = override.signalIds ?? [];
	        const target = override.signalIds === undefined ? definition.target
	            : onlySignalId === undefined ? { component: true }
	                : moreSignalIds.length === 0 ? { signal: onlySignalId }
	                    : { signal: override.signalIds };
	        return {
	            ...bound,
	            boundary: {
	                ...withoutRelative,
	                ...(relative === undefined ? {} : { relative_to: relative }),
	                target,
	                ...(override.level === undefined ? {} : { level: override.level }),
	                numerical,
	            },
	        };
	    }
	    static changeSlot(numerical, slot, value, inclusive) {
	        if (slot === 'typ') {
	            if (value === undefined) {
	                return numerical;
	            }
	            return numerical.type === 'absolute' ? { ...numerical, value } : { ...numerical, nominalValue: value };
	        }
	        switch (numerical.type) {
	            case 'range': {
	                if (slot === 'min') {
	                    const { minValIncluded, ...rest } = { ...numerical, minVal: value ?? numerical.minVal };
	                    const open = inclusive === undefined ? minValIncluded
	                        : inclusive ? undefined
	                            : false;
	                    return open === undefined ? rest : { ...rest, minValIncluded: open };
	                }
	                const { maxValIncluded, ...rest } = { ...numerical, maxVal: value ?? numerical.maxVal };
	                const open = inclusive === undefined ? maxValIncluded
	                    : inclusive ? undefined
	                        : false;
	                return open === undefined ? rest : { ...rest, maxValIncluded: open };
	            }
	            case 'minimum':
	            case 'maximum': {
	                if ((numerical.type === 'minimum') !== (slot === 'min')) {
	                    return numerical;
	                }
	                const { valInclusive, ...rest } = { ...numerical, value: value ?? numerical.value };
	                const open = inclusive === undefined ? valInclusive
	                    : inclusive ? undefined
	                        : false;
	                return open === undefined ? rest : { ...rest, valInclusive: open };
	            }
	            case 'absolute':
	                return numerical;
	        }
	    }
	    static changes(expected, actual) {
	        const expectedDefinition = expected.boundary;
	        const actualDefinition = actual.boundary;
	        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(expectedDefinition) ||
	            !sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(actualDefinition)) {
	            return [];
	        }
	        const changes = [];
	        const expectedLimits = BoundaryFacts.limits(expectedDefinition.numerical);
	        for (const actualLimit of BoundaryFacts.limits(actualDefinition.numerical)) {
	            const expectedLimit = expectedLimits.find((limit) => limit.slot === actualLimit.slot);
	            const valueChanged = expectedLimit === undefined || !BoundaryFacts.sameValue(expectedLimit.value, actualLimit.value);
	            const inclusiveChanged = expectedLimit === undefined || expectedLimit.inclusive !== actualLimit.inclusive;
	            if (!valueChanged && !inclusiveChanged) {
	                continue;
	            }
	            changes.push({
	                op: ExpressionExpansion_1.BoundOverrideOp.Change,
	                id: expected.id,
	                slot: actualLimit.slot,
	                ...(valueChanged ? { value: actualLimit.value } : {}),
	                ...(inclusiveChanged ? { inclusive: actualLimit.inclusive } : {}),
	            });
	        }
	        changes.sort((a, b) => SLOT_ORDER.indexOf(a.slot ?? 'min') - SLOT_ORDER.indexOf(b.slot ?? 'min'));
	        const expectedRelative = BoundaryFacts.relativeOf(expectedDefinition.relative_to);
	        const actualRelative = BoundaryFacts.relativeOf(actualDefinition.relative_to);
	        if (expectedRelative !== actualRelative) {
	            changes.push({ op: ExpressionExpansion_1.BoundOverrideOp.Change, id: expected.id, relativeSignalId: actualRelative });
	        }
	        const expectedTargets = BoundaryFacts.targetKey(expectedDefinition.target);
	        const actualTargets = BoundaryFacts.targetKey(actualDefinition.target);
	        if (expectedTargets !== actualTargets) {
	            const signalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([], actualDefinition.target).map(String);
	            changes.push({ op: ExpressionExpansion_1.BoundOverrideOp.Change, id: expected.id, signalIds });
	        }
	        if (expectedDefinition.level !== actualDefinition.level && actualDefinition.level !== undefined) {
	            changes.push({ op: ExpressionExpansion_1.BoundOverrideOp.Change, id: expected.id, level: actualDefinition.level });
	        }
	        return changes;
	    }
	    static shapeKey(bound) {
	        const definition = bound.boundary;
	        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
	            return JSON.stringify(bound.boundary);
	        }
	        const { numerical } = definition;
	        return JSON.stringify([
	            definition.class ?? null,
	            definition.strength ?? null,
	            numerical.type,
	            numerical.characteristic,
	            numerical.type === 'range' ? (numerical.rangeMode ?? null) : null,
	            numerical.variance ?? null,
	            numerical.useAbsVal ?? null,
	            numerical.type !== 'absolute' && numerical.nominalValue !== undefined,
	            BoundaryFacts.conditionsKey(bound),
	        ]);
	    }
	    static placementKey(bound) {
	        return JSON.stringify([
	            BoundaryFacts.targetKey(bound.boundary.target),
	            BoundaryFacts.relativeOf(bound.boundary.relative_to),
	        ]);
	    }
	    static targetKey(target) {
	        if ('component' in target) {
	            return '*';
	        }
	        return JSON.stringify((0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([], target).map(String).sort());
	    }
	    static relativeOf(relative) {
	        const [first] = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([], relative);
	        return first === undefined ? null : String(first);
	    }
	    static sorted(facts) {
	        return [...facts].sort((a, b) => {
	            const partsA = BoundaryFacts.identityParts(a);
	            const partsB = BoundaryFacts.identityParts(b);
	            for (const [index, partA] of partsA.entries()) {
	                const partB = partsB[index] ?? '';
	                if (partA !== partB) {
	                    return partA < partB ? -1 : 1;
	                }
	            }
	            return a.value - b.value;
	        });
	    }
	    static distinct(facts) {
	        const distinct = [];
	        for (const fact of BoundaryFacts.sorted([...facts])) {
	            const previous = distinct[distinct.length - 1];
	            if (previous === undefined || !BoundaryFacts.sameFact(previous, fact)) {
	                distinct.push(fact);
	            }
	        }
	        return distinct;
	    }
	    static identity(fact) {
	        return JSON.stringify(BoundaryFacts.identityParts(fact));
	    }
	    static identityParts(fact) {
	        return [
	            fact.signalId ?? '',
	            fact.characteristic,
	            String(SLOT_ORDER.indexOf(fact.slot)),
	            fact.relativeSignalId ?? '',
	            fact.conditionsKey,
	            fact.inclusive ? '1' : '0',
	            fact.level ?? '',
	        ];
	    }
	    static sameFact(a, b) {
	        return (b !== null && BoundaryFacts.identity(a) === BoundaryFacts.identity(b) && BoundaryFacts.sameValue(a.value, b.value));
	    }
	    static sameValue(a, b) {
	        return Math.abs(a - b) <= BoundaryFacts.RELATIVE_EPSILON * Math.max(Math.abs(a), Math.abs(b));
	    }
	    static isRated(characteristic) {
	        return sigma_engine_shared_1.RatedBoundaryCharacteristics.includes(characteristic);
	    }
	}
	boundaryFacts.BoundaryFacts = BoundaryFacts;
	
	return boundaryFacts;
}

var orphanBoundaries = {};

var hasRequiredOrphanBoundaries;

function requireOrphanBoundaries () {
	if (hasRequiredOrphanBoundaries) return orphanBoundaries;
	hasRequiredOrphanBoundaries = 1;
	Object.defineProperty(orphanBoundaries, "__esModule", { value: true });
	orphanBoundaries.OrphanBoundaries = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const ExpressionExpansion_1 = requireExpressionExpansion();
	const boundary_facts_1 = requireBoundaryFacts();
	class OrphanBoundaries {
	    static kindClass(kind) {
	        switch (kind) {
	            case ExpressionExpansion_1.RatingsKind.Amr: {
	                return sigma_engine_shared_1.BoundaryClass.IC_AbsoluteMaximumRating;
	            }
	            case ExpressionExpansion_1.RatingsKind.Oc: {
	                return sigma_engine_shared_1.BoundaryClass.IC_ElectricalCharacteristics;
	            }
	        }
	    }
	    static isOfKind(bound, kind) {
	        return bound.boundary.class === OrphanBoundaries.kindClass(kind);
	    }
	    static isStatableByRow(bound) {
	        const definition = bound.boundary;
	        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
	            return false;
	        }
	        const { numerical } = definition;
	        return (sigma_engine_shared_1.RatedBoundaryCharacteristics.includes(numerical.characteristic) &&
	            (definition.strength === undefined || definition.strength === sigma_engine_shared_1.BoundaryAssertionStrength.None) &&
	            (numerical.variance === undefined || numerical.variance === '') &&
	            numerical.useAbsVal !== true &&
	            !(numerical.type === 'range' && numerical.rangeMode === 'exclusive') &&
	            !OrphanBoundaries.namesSeveralSignals(definition.relative_to) &&
	            !(bound.conditions ?? []).some((condition) => sigma_engine_shared_1.DesignModels.isModeReferenceCondition(condition) && !condition.mode_state));
	    }
	    static expandRows(rows, context) {
	        return Object.fromEntries(rows.map((row) => [row.id, ExpressionExpansion_1.RatingExpansionEngine.expand(row, context)]));
	    }
	    static statedBounds(rows, context) {
	        const kindClass = OrphanBoundaries.kindClass(context.kind);
	        return rows.flatMap((row) => boundary_facts_1.BoundaryFacts.apply(ExpressionExpansion_1.RatingExpansionEngine.expand(row, context).bounds, row.overrides).map((bound) => bound.boundary.class === undefined ? { ...bound, boundary: { ...bound.boundary, class: kindClass } } : bound));
	    }
	    static find(rows, context, boundaries, deletedBoundaryIds) {
	        const stated = OrphanBoundaries.statedFacts(rows, context);
	        const deleted = new Set(deletedBoundaryIds);
	        return boundaries.filter((bound) => !deleted.has(bound.id) &&
	            OrphanBoundaries.isOfKind(bound, context.kind) &&
	            !OrphanBoundaries.isCovered(bound, stated, context));
	    }
	    static covered(rows, context, boundaries) {
	        const stated = OrphanBoundaries.statedFacts(rows, context);
	        return boundaries.filter((bound) => OrphanBoundaries.isOfKind(bound, context.kind) && OrphanBoundaries.isCovered(bound, stated, context));
	    }
	    static rowsNeedingReference(rows, expansions) {
	        return rows
	            .filter((row) => row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Voltage &&
	            OrphanBoundaries.lacksReference(row.relative) &&
	            expansions[row.id]?.status === ExpressionExpansion_1.ExpansionStatus.Unrecognized)
	            .map((row) => row.id);
	    }
	    static namesSeveralSignals(reference) {
	        return (reference !== undefined && 'signal' in reference && Array.isArray(reference.signal) && reference.signal.length > 1);
	    }
	    static lacksReference(relative) {
	        if (relative === null) {
	            return true;
	        }
	        switch (relative.kind) {
	            case ExpressionExpansion_1.RatingsRelativeReferenceKind.Signal: {
	                return relative.signalId === null;
	            }
	            case ExpressionExpansion_1.RatingsRelativeReferenceKind.Mapping: {
	                return relative.pairs.some((pair) => pair.relativeId === null);
	            }
	        }
	    }
	    static statedFacts(rows, context) {
	        return boundary_facts_1.BoundaryFacts.fromBoundaries(OrphanBoundaries.statedBounds(rows, context).filter((bound) => OrphanBoundaries.isOfKind(bound, context.kind) && OrphanBoundaries.isStatableByRow(bound)), context.signals);
	    }
	    static isCovered(bound, stated, context) {
	        if (!OrphanBoundaries.isStatableByRow(bound)) {
	            return false;
	        }
	        const facts = boundary_facts_1.BoundaryFacts.fromBoundaries([bound], context.signals);
	        return boundary_facts_1.BoundaryFacts.factsEqual([...stated, ...facts], stated);
	    }
	}
	orphanBoundaries.OrphanBoundaries = OrphanBoundaries;
	
	return orphanBoundaries;
}

var signalGroupRegistry = {};

var hasRequiredSignalGroupRegistry;

function requireSignalGroupRegistry () {
	if (hasRequiredSignalGroupRegistry) return signalGroupRegistry;
	hasRequiredSignalGroupRegistry = 1;
	Object.defineProperty(signalGroupRegistry, "__esModule", { value: true });
	signalGroupRegistry.SignalGroupRegistry = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	class SignalGroupRegistry {
	    static { this.NAMED_MEMBERS = 3; }
	    constructor(existing, signals) {
	        this.signals = signals;
	        this.groups = existing.map((group) => ({ ...group, signalIds: [...group.signalIds] }));
	    }
	    groupFor(signalIds) {
	        const members = [...new Set(signalIds)].sort();
	        const key = members.join(',');
	        const existing = this.groups.find((group) => [...group.signalIds].sort().join(',') === key);
	        if (existing !== undefined) {
	            return existing.id;
	        }
	        const id = `grp_${sigma_engine_shared_1.Utils.fnv1aHash(key)}`;
	        this.groups.push({ id, name: this.nameFor(members), signalIds: members });
	        return id;
	    }
	    all() {
	        return this.groups.map((group) => ({ ...group, signalIds: [...group.signalIds] }));
	    }
	    nameFor(members) {
	        const names = members.map((id) => this.signals.find((signal) => String(signal.id) === id)?.name ?? id);
	        const shown = names.slice(0, SignalGroupRegistry.NAMED_MEMBERS).join(', ');
	        const rest = names.length - SignalGroupRegistry.NAMED_MEMBERS;
	        return rest > 0 ? `${shown} +${rest}` : shown;
	    }
	}
	signalGroupRegistry.SignalGroupRegistry = SignalGroupRegistry;
	
	return signalGroupRegistry;
}

var hasRequiredRatingsTableCodec;

function requireRatingsTableCodec () {
	if (hasRequiredRatingsTableCodec) return ratingsTableCodec;
	hasRequiredRatingsTableCodec = 1;
	Object.defineProperty(ratingsTableCodec, "__esModule", { value: true });
	ratingsTableCodec.RatingsTableCodec = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const ExpressionExpansion_1 = requireExpressionExpansion();
	const operating_condition_modes_1 = requireOperatingConditionModes();
	const boundary_facts_1 = requireBoundaryFacts();
	const orphan_boundaries_1 = requireOrphanBoundaries();
	const ratings_table_types_1 = requireRatingsTable_types();
	const signal_group_registry_1 = requireSignalGroupRegistry();
	const stored_ratings_table_1 = requireStoredRatingsTable();
	class RatingsTableCodec {
	    static toRatingsTable(kind, signals, modes, boundaries, storedTable, parentRows) {
	        const conditions = RatingsTableCodec.conditionsOf(modes, signals);
	        const pairs = RatingsTableCodec.pairsOf(signals);
	        const groups = new signal_group_registry_1.SignalGroupRegistry(storedTable?.signalGroups ?? [], signals);
	        const storedRows = storedTable?.rows ?? [];
	        const storedRowIds = new Set(storedRows.map((row) => row.id));
	        const prefilled = [];
	        for (const parentRow of parentRows) {
	            if (storedRowIds.has(parentRow.id)) {
	                continue;
	            }
	            const recorded = new Set(parentRow.boundaryIds);
	            const bounds = boundaries.filter((bound) => recorded.has(bound.id) && RatingsTableCodec.isPrefillable(bound, kind));
	            if (bounds.length === 0) {
	                continue;
	            }
	            prefilled.push(RatingsTableCodec.prefilledRow(parentRow, bounds, signals, groups));
	        }
	        return {
	            formVersion: stored_ratings_table_1.RATINGS_TABLE_FORM_VERSION,
	            kind,
	            signalGroups: groups.all(),
	            conditions,
	            pairs,
	            rows: [...storedRows, ...prefilled],
	        };
	    }
	    static toBoundaries(kind, signals, modes, boundaries, table, edits, parentRows) {
	        const savedModes = [...modes];
	        const modeIdByConditionId = new Map();
	        for (const condition of table.conditions) {
	            if (modes.some((mode) => mode.id === condition.id)) {
	                modeIdByConditionId.set(condition.id, condition.id);
	                continue;
	            }
	            if (condition.value.targetSignalId === null || condition.value.relativeSignalId === null) {
	                continue;
	            }
	            const targetId = condition.value.targetSignalId;
	            const targetName = signals.find((signal) => String(signal.id) === targetId)?.name ?? targetId;
	            const mode = operating_condition_modes_1.OperatingConditionModes.toMode(condition.value, targetName);
	            modeIdByConditionId.set(condition.id, mode.id);
	            if (!savedModes.some((existing) => existing.id === mode.id)) {
	                savedModes.push(mode);
	            }
	        }
	        const context = {
	            kind,
	            signalGroups: table.signalGroups,
	            conditions: table.conditions.flatMap((condition) => {
	                const modeId = modeIdByConditionId.get(condition.id);
	                return modeId === undefined ? [] : [{ ...condition, id: modeId }];
	            }),
	            signals,
	            pairs: RatingsTableCodec.expansionPairs(table.pairs),
	        };
	        const rows = table.rows.map((row) => ({
	            ...row,
	            conditionIds: row.conditionIds.map((id) => modeIdByConditionId.get(id) ?? id),
	        }));
	        const rowsNeedingReference = orphan_boundaries_1.OrphanBoundaries.rowsNeedingReference(rows, orphan_boundaries_1.OrphanBoundaries.expandRows(rows, context));
	        if (rowsNeedingReference.length > 0) {
	            return { outcome: ratings_table_types_1.RatingsTableSaveOutcome.Refused, rowsNeedingReference };
	        }
	        const dropped = new Set([
	            ...orphan_boundaries_1.OrphanBoundaries.covered(rows, context, boundaries).map((bound) => bound.id),
	            ...edits.deletedBoundaryIds,
	            ...(edits.dropUnstatedRowBounds === true ?
	                RatingsTableCodec.rowRecordedBoundIds(kind, boundaries, parentRows)
	                : []),
	        ]);
	        const kept = boundaries.filter((bound) => !dropped.has(bound.id));
	        const minted = [];
	        const mintedIds = new Set();
	        const savedRows = rows.map((row) => {
	            const bounds = orphan_boundaries_1.OrphanBoundaries.statedBounds([row], context);
	            for (const bound of bounds) {
	                if (!mintedIds.has(bound.id)) {
	                    mintedIds.add(bound.id);
	                    minted.push(bound);
	                }
	            }
	            return { ...row, boundaryIds: bounds.map((bound) => bound.id) };
	        });
	        const parentRowById = new Map(parentRows.map((parentRow) => [parentRow.id, parentRow]));
	        const rowIds = new Set(savedRows.map((row) => row.id));
	        const savedBoundIds = new Set([...kept, ...minted].map((bound) => bound.id));
	        const carried = parentRows.filter((parentRow) => !rowIds.has(parentRow.id) && parentRow.boundaryIds.some((id) => savedBoundIds.has(id)));
	        return {
	            outcome: ratings_table_types_1.RatingsTableSaveOutcome.Saved,
	            boundaries: [...kept, ...minted],
	            modes: savedModes,
	            table: {
	                formVersion: stored_ratings_table_1.RATINGS_TABLE_FORM_VERSION,
	                kind,
	                signalGroups: table.signalGroups,
	                rows: savedRows,
	            },
	            parentRows: [
	                ...savedRows.map((row) => RatingsTableCodec.parentRowOf(row, parentRowById.get(row.id))),
	                ...carried,
	            ],
	        };
	    }
	    static expansionContext(table, signals) {
	        return {
	            kind: table.kind,
	            signalGroups: table.signalGroups,
	            conditions: table.conditions,
	            signals,
	            pairs: RatingsTableCodec.expansionPairs(table.pairs),
	        };
	    }
	    static prefilledRow(parentRow, bounds, signals, groups) {
	        const characteristic = boundary_facts_1.BoundaryFacts.fromBoundaries(bounds, signals)[0]?.characteristic ?? sigma_engine_shared_1.BoundaryCharacteristic.Voltage;
	        const signalIds = [
	            ...new Set(bounds.flatMap((bound) => (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([...signals], bound.boundary.target)
	                .map(String)
	                .filter((id) => signals.some((signal) => String(signal.id) === id)))),
	        ];
	        const relative = characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Voltage ?
	            { kind: ExpressionExpansion_1.RatingsRelativeReferenceKind.Signal, signalId: null }
	            : null;
	        const targets = characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Power ? [] : signalIds;
	        const grouped = targets.length > stored_ratings_table_1.MAX_ROW_BARE_SIGNALS;
	        return {
	            id: parentRow.id,
	            origin: ExpressionExpansion_1.RatingRowOrigin.Entered,
	            characteristic,
	            description: parentRow.description,
	            min: parentRow.min ?? '',
	            typ: parentRow.typ ?? '',
	            max: parentRow.max ?? '',
	            relative,
	            conditionIds: RatingsTableCodec.conditionIdsOf(bounds),
	            groupIds: grouped ? [groups.groupFor(targets)] : [],
	            ...(grouped || targets.length === 0 ? {} : { signalIds: [...targets].sort() }),
	            boundaryIds: [...parentRow.boundaryIds],
	            overrides: [],
	        };
	    }
	    static rowRecordedBoundIds(kind, boundaries, parentRows) {
	        const recorded = new Set(parentRows.flatMap((parentRow) => parentRow.boundaryIds));
	        return boundaries
	            .filter((bound) => recorded.has(bound.id) && orphan_boundaries_1.OrphanBoundaries.isOfKind(bound, kind) && orphan_boundaries_1.OrphanBoundaries.isStatableByRow(bound))
	            .map((bound) => bound.id);
	    }
	    static isPrefillable(bound, kind) {
	        return (orphan_boundaries_1.OrphanBoundaries.isOfKind(bound, kind) &&
	            orphan_boundaries_1.OrphanBoundaries.isStatableByRow(bound) &&
	            bound.boundary.level === (0, ExpressionExpansion_1.kindDefaultLevel)(kind));
	    }
	    static conditionIdsOf(bounds) {
	        const ids = new Set();
	        for (const bound of bounds) {
	            for (const condition of bound.conditions ?? []) {
	                if (sigma_engine_shared_1.DesignModels.isModeReferenceCondition(condition) && condition.mode_state) {
	                    ids.add(condition.mode);
	                }
	            }
	        }
	        return [...ids].sort();
	    }
	    static conditionsOf(modes, signals) {
	        return modes
	            .map((mode) => operating_condition_modes_1.OperatingConditionModes.fromMode(mode, signals))
	            .filter((condition) => condition !== null)
	            .sort((a, b) => (a.id < b.id ? -1 : 1));
	    }
	    static pairsOf(signals) {
	        const byLegs = new Map();
	        for (const signal of signals) {
	            const pair = signal.diffPair;
	            if (pair === undefined) {
	                continue;
	            }
	            const positiveSignalId = pair.positiveId === '' ? null : String(pair.positiveId);
	            const negativeSignalId = pair.negativeId === '' ? null : String(pair.negativeId);
	            const key = `${positiveSignalId ?? ''}|${negativeSignalId ?? ''}`;
	            if (!byLegs.has(key)) {
	                byLegs.set(key, {
	                    id: `pair_${sigma_engine_shared_1.Utils.fnv1aHash(key)}`,
	                    name: pair.pairName,
	                    positiveSignalId,
	                    negativeSignalId,
	                });
	            }
	        }
	        return [...byLegs.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, pair]) => pair);
	    }
	    static expansionPairs(pairs) {
	        const complete = [];
	        for (const pair of pairs) {
	            if (pair.positiveSignalId !== null && pair.negativeSignalId !== null) {
	                complete.push({ positiveId: pair.positiveSignalId, negativeId: pair.negativeSignalId });
	            }
	        }
	        return complete;
	    }
	    static parentRowOf(row, previous) {
	        return {
	            id: row.id,
	            description: row.description,
	            symbol: previous?.symbol ?? null,
	            min: row.min === '' ? null : row.min,
	            typ: row.typ === '' ? null : row.typ,
	            max: row.max === '' ? null : row.max,
	            boundaryIds: row.boundaryIds,
	            ...(previous?.sourceRows === undefined ? {} : { sourceRows: previous.sourceRows }),
	        };
	    }
	}
	ratingsTableCodec.RatingsTableCodec = RatingsTableCodec;
	
	return ratingsTableCodec;
}

var hasRequiredRatingsTable;

function requireRatingsTable () {
	if (hasRequiredRatingsTable) return RatingsTable;
	hasRequiredRatingsTable = 1;
	(function (exports) {
		var __createBinding = (RatingsTable && RatingsTable.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (RatingsTable && RatingsTable.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireRatingsTable_types(), exports);
		__exportStar(requireStoredRatingsTable(), exports);
		__exportStar(requireRatingsTableCodec(), exports);
		__exportStar(requireBoundaryFacts(), exports);
		__exportStar(requireSignalGroupRegistry(), exports);
		__exportStar(requireOrphanBoundaries(), exports);
		
	} (RatingsTable));
	return RatingsTable;
}

var NetlistValidation = {};

var netlistValidation = {};

var types$1 = {};

var hasRequiredTypes$1;

function requireTypes$1 () {
	if (hasRequiredTypes$1) return types$1;
	hasRequiredTypes$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NetlistValidationResultSchema = exports.NetlistValidationSummarySchema = exports.UnknownDesignatorsSchema = exports.OracleUnknownConnectionsSchema = exports.OracleRejectedRowsSchema = exports.NetlistValidationErrorSchema = exports.NetConnectionSchema = exports.NO_CONNECT_PLACEHOLDER_NET_NAME = exports.NetlistValidationErrorType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const netlist_gate_1 = requireNetlistGate();
		var NetlistValidationErrorType;
		(function (NetlistValidationErrorType) {
		    NetlistValidationErrorType["NetNameMismatch"] = "NetNameMismatch";
		    NetlistValidationErrorType["MissingModuleConnection"] = "MissingModuleConnection";
		    NetlistValidationErrorType["UnexpectedProjectModelNet"] = "UnexpectedProjectModelNet";
		    NetlistValidationErrorType["UnexpectedNetFound"] = "UnexpectedNetFound";
		    NetlistValidationErrorType["UnexpectedConnection"] = "UnexpectedConnection";
		    NetlistValidationErrorType["ModuleNotFoundInNetlist"] = "ModuleNotFoundInNetlist";
		    NetlistValidationErrorType["ValidationError"] = "ValidationError";
		    NetlistValidationErrorType["MissingModule"] = "MissingModule";
		})(NetlistValidationErrorType || (exports.NetlistValidationErrorType = NetlistValidationErrorType = {}));
		exports.NO_CONNECT_PLACEHOLDER_NET_NAME = '?';
		exports.NetConnectionSchema = zod_1.z.object({
		    moduleName: zod_1.z.string(),
		    pinRef: zod_1.z.string(),
		    pinFunction: zod_1.z.string().optional(),
		});
		exports.NetlistValidationErrorSchema = zod_1.z
		    .object({
		    type: zod_1.z.nativeEnum(NetlistValidationErrorType),
		    message: zod_1.z.string(),
		    severity: zod_1.z.nativeEnum(sigma_engine_shared_1.BoundaryLevel).optional(),
		    projectNetName: zod_1.z.string().optional(),
		    netlistNetName: zod_1.z.string().optional(),
		    moduleName: zod_1.z.string().optional(),
		    pinRef: zod_1.z.string().optional(),
		    connections: exports.NetConnectionSchema.array().optional(),
		    actualConnections: exports.NetConnectionSchema.array().optional(),
		})
		    .passthrough();
		exports.OracleRejectedRowsSchema = zod_1.z.object({
		    lineNumbers: zod_1.z.number().array(),
		});
		exports.OracleUnknownConnectionsSchema = zod_1.z.object({
		    connections: exports.NetConnectionSchema.array(),
		});
		exports.UnknownDesignatorsSchema = zod_1.z.object({
		    count: zod_1.z.number(),
		    matchedInOtherForm: zod_1.z.number(),
		});
		exports.NetlistValidationSummarySchema = zod_1.z.object({
		    totalNetsChecked: zod_1.z.number(),
		    totalErrorsFound: zod_1.z.number(),
		    netNameMismatches: zod_1.z.number(),
		    missingConnections: zod_1.z.number(),
		    missingNets: zod_1.z.number(),
		    missingModules: zod_1.z.number(),
		    unexpectedNets: zod_1.z.number(),
		    oracleRejectedRows: exports.OracleRejectedRowsSchema.optional(),
		    oracleUnknownConnections: exports.OracleUnknownConnectionsSchema.optional(),
		    unknownDesignators: exports.UnknownDesignatorsSchema.optional(),
		});
		exports.NetlistValidationResultSchema = zod_1.z
		    .object({
		    type: zod_1.z.literal('NetlistValidation'),
		    message: zod_1.z.string(),
		    severity: zod_1.z.nativeEnum(sigma_engine_shared_1.BoundaryLevel).optional(),
		    errors: exports.NetlistValidationErrorSchema.array(),
		    netlistSource: zod_1.z.nativeEnum(netlist_gate_1.NetlistSource).optional(),
		    summary: exports.NetlistValidationSummarySchema,
		})
		    .passthrough();
		
	} (types$1));
	return types$1;
}

var hasRequiredNetlistValidation$1;

function requireNetlistValidation$1 () {
	if (hasRequiredNetlistValidation$1) return netlistValidation;
	hasRequiredNetlistValidation$1 = 1;
	Object.defineProperty(netlistValidation, "__esModule", { value: true });
	netlistValidation.validateNetlist = validateNetlist;
	netlistValidation.findModelModulesAbsentFromNetlist = findModelModulesAbsentFromNetlist;
	netlistValidation.getOracleRejectedRowLineNumbers = getOracleRejectedRowLineNumbers;
	const sigma_engine_shared_1 = requireDist$1();
	const types_1 = requireTypes$1();
	const pcadNetlistParser_1 = requirePcadNetlistParser();
	const PCAD_MAX_NET_NAME_LENGTH = 14;
	function isPcadTruncationPlaceholder(netName) {
	    return /^NET\d{5}$/.test(netName);
	}
	function normalizePinRef(pinRef) {
	    return pinRef.replace(/,\s*/g, ',').replace(/ /g, '_');
	}
	function findDuplicatePinGroup(connection, moduleMapping, duplicatePinCatalog) {
	    const physicalModuleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
	    const groupsByModuleRef = duplicatePinCatalog.get(physicalModuleName);
	    if (!groupsByModuleRef) {
	        return undefined;
	    }
	    const candidateGroups = connection.moduleRef !== undefined ?
	        [groupsByModuleRef.get(connection.moduleRef) ?? new Map()]
	        : [...groupsByModuleRef.values()];
	    for (const groupsByNumber of candidateGroups) {
	        for (const group of groupsByNumber.values()) {
	            if (group.some((pin) => pin.ref === connection.pinRef)) {
	                return group;
	            }
	        }
	    }
	    return undefined;
	}
	function getComparablePinRef(connection, moduleMapping, duplicatePinCatalog) {
	    const group = findDuplicatePinGroup(connection, moduleMapping, duplicatePinCatalog);
	    return group?.find((pin) => pin.ref === connection.pinRef)?.number ?? connection.pinRef;
	}
	function isRepeatedModelPinNumber(connection, moduleMapping, duplicatePinCatalog) {
	    const physicalModuleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
	    const groupsByModelModuleName = duplicatePinCatalog.get(physicalModuleName);
	    if (!groupsByModelModuleName) {
	        return false;
	    }
	    return [...groupsByModelModuleName.values()].some((groupsByNumber) => groupsByNumber.has(connection.pinRef));
	}
	function connectionsMatch(projectConnection, netlistConnection, moduleMapping, duplicatePinCatalog) {
	    const group = findDuplicatePinGroup(projectConnection, moduleMapping, duplicatePinCatalog);
	    const projectPin = group?.find((pin) => pin.ref === projectConnection.pinRef);
	    if (!group || !projectPin) {
	        return pinRefsMatch(projectConnection.pinRef, netlistConnection.pinRef);
	    }
	    if (!pinRefsMatch(projectPin.number, netlistConnection.pinRef)) {
	        return false;
	    }
	    const pinFunction = netlistConnection.pinFunction;
	    if (!pinFunction || !projectPin.name || projectPin.name === '~' || projectPin.name === pinFunction) {
	        return true;
	    }
	    const functionPin = group.find((pin) => pin.name === pinFunction);
	    return functionPin?.netName === projectPin.netName;
	}
	function buildDuplicatePinCatalog(projectModel, moduleMapping) {
	    const catalog = new Map();
	    if (projectModel.type !== 'kicad') {
	        return catalog;
	    }
	    for (const design of Object.values(projectModel.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        for (const module of design.modules) {
	            const component = projectModel.components[module.componentRef];
	            const symbol = component?.symbols.find((candidate) => candidate.name === module.footprintRef);
	            if (!symbol) {
	                continue;
	            }
	            const pinsByNumber = new Map();
	            for (const pin of symbol.pins) {
	                const pins = pinsByNumber.get(pin.number) ?? [];
	                pins.push(pin);
	                pinsByNumber.set(pin.number, pins);
	            }
	            const repeatedPins = [...pinsByNumber.entries()].filter(([, pins]) => pins.length > 1);
	            if (repeatedPins.length === 0) {
	                continue;
	            }
	            const physicalModuleName = moduleMapping.get(module.name) ?? module.name;
	            const groupsByModuleRef = catalog.get(physicalModuleName) ?? new Map();
	            const groupsByNumber = groupsByModuleRef.get(module.ref) ?? new Map();
	            for (const [number, pins] of repeatedPins) {
	                groupsByNumber.set(number, pins.map((pin) => ({
	                    ref: pin.ref,
	                    number: pin.number,
	                    name: pin.name,
	                    netName: module.pinNets[pin.ref],
	                })));
	            }
	            groupsByModuleRef.set(module.ref, groupsByNumber);
	            catalog.set(physicalModuleName, groupsByModuleRef);
	        }
	    }
	    return catalog;
	}
	function pinRefsMatch(pinRef1, pinRef2) {
	    if (pinRef1 === pinRef2) {
	        return true;
	    }
	    return normalizePinRef(pinRef1) === normalizePinRef(pinRef2);
	}
	function findNetlistConnection(pinRef, netlistModule) {
	    if (netlistModule.pinNets[pinRef] !== undefined) {
	        return netlistModule.pinNets[pinRef];
	    }
	    for (const [netlistPinRef, netName] of Object.entries(netlistModule.pinNets)) {
	        if (pinRefsMatch(pinRef, netlistPinRef)) {
	            return netName;
	        }
	    }
	    return undefined;
	}
	function findProjectConnection(pinRef, designModule) {
	    if (designModule.pinNets[pinRef] !== undefined) {
	        return designModule.pinNets[pinRef];
	    }
	    for (const [projectPinRef, netName] of Object.entries(designModule.pinNets)) {
	        if (pinRefsMatch(pinRef, projectPinRef)) {
	            return netName;
	        }
	    }
	    return undefined;
	}
	function buildConnectionSignature(connections, pinRefResolver) {
	    const parts = new Set();
	    for (const conn of connections) {
	        parts.add(`${conn.moduleName}\0${normalizePinRef(pinRefResolver?.(conn) ?? conn.pinRef)}`);
	    }
	    return [...parts].sort().join('\x01');
	}
	function buildRealConnectionIndex(nets, moduleMapping, virtualModuleNames, pinRefResolver) {
	    const index = new Map();
	    for (const [netName, netInfo] of nets) {
	        const realConnections = netInfo.connections.flatMap((connection) => {
	            const moduleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
	            return virtualModuleNames.has(moduleName) ?
	                []
	                : [{ moduleName, moduleRef: connection.moduleRef, pinRef: connection.pinRef }];
	        });
	        if (realConnections.length === 0) {
	            continue;
	        }
	        const signature = buildConnectionSignature(realConnections, pinRefResolver);
	        const matches = index.get(signature) ?? [];
	        matches.push([netName, netInfo]);
	        index.set(signature, matches);
	    }
	    return index;
	}
	function normalizeNetName(name, options) {
	    let n = name;
	    if (options.ignoreWhitespace) {
	        n = n.replace(/\s+/g, '');
	    }
	    if (options.ignoreCase) {
	        n = n.toLowerCase();
	    }
	    return n;
	}
	function buildNormalizedNetIndex(nets, options) {
	    const index = new Map();
	    if (!options.ignoreCase && !options.ignoreWhitespace) {
	        return index;
	    }
	    for (const [netName, netInfo] of nets) {
	        const normalized = normalizeNetName(netName, options);
	        if (!index.has(normalized)) {
	            index.set(normalized, [netName, netInfo]);
	        }
	    }
	    return index;
	}
	function findProjectNetForOracleConnection(oracleConnection, projectConnectionIndex) {
	    return projectConnectionIndex.get(buildConnectionSignature([oracleConnection]))?.[1];
	}
	function buildReferenceModulesByName(modules) {
	    const modulesByName = new Map();
	    for (const module of modules) {
	        const existingModule = modulesByName.get(module.name);
	        if (!existingModule) {
	            modulesByName.set(module.name, { ...module, pinNets: { ...module.pinNets } });
	            continue;
	        }
	        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
	            if (netName !== types_1.NO_CONNECT_PLACEHOLDER_NET_NAME || existingModule.pinNets[pinRef] === undefined) {
	                existingModule.pinNets[pinRef] = netName;
	            }
	        }
	        modulesByName.set(module.name, { ...module, pinNets: existingModule.pinNets });
	    }
	    return modulesByName;
	}
	function getEffectiveNoConnectDeclarations(declarations, realNets) {
	    if (!declarations) {
	        return undefined;
	    }
	    const connectedPinKeys = new Set();
	    for (const net of realNets.values()) {
	        for (const connection of net.connections) {
	            connectedPinKeys.add(buildConnectionSignature([connection]));
	        }
	    }
	    return {
	        ...declarations,
	        connections: declarations.connections.filter((declaration) => !connectedPinKeys.has(buildConnectionSignature([declaration]))),
	    };
	}
	function validateNetlist(projectModel, netlistSchematic, options = {}) {
	    const errors = [];
	    const opts = {
	        ignoreCase: false,
	        ignoreWhitespace: false,
	        ...options,
	    };
	    const modulesByName = new Map();
	    for (const mod of projectModel.modules) {
	        modulesByName.set(mod.name, mod);
	    }
	    const netlistModulesByName = buildReferenceModulesByName(netlistSchematic.modules);
	    const virtualModuleNames = new Set();
	    for (const mod of projectModel.modules) {
	        const component = projectModel.components[mod.componentRef];
	        if (component?.isVirtual === true) {
	            virtualModuleNames.add(mod.name);
	        }
	    }
	    const moduleMapping = buildModuleMapping(projectModel);
	    const projectNets = new Map();
	    for (const design of Object.values(projectModel.designs)) {
	        if (design.modelType !== 'schematic') {
	            continue;
	        }
	        for (const [netName, netInfo] of Object.entries(design.nets)) {
	            if (netInfo.metadata?.[sigma_engine_shared_1.VIRTUAL_NET_METADATA_KEY] === sigma_engine_shared_1.NO_CONNECT_VIRTUAL_NET_METADATA_VALUE) {
	                continue;
	            }
	            const isAutoNamed = netName.includes('__UNNAMED_NET_');
	            const key = isAutoNamed ? `${design.designId}\0${netName}` : netName;
	            const existingNet = projectNets.get(key);
	            if (existingNet) {
	                const seen = new Set();
	                for (const conn of existingNet.connections) {
	                    seen.add(`${conn.moduleRef}\0${conn.pinRef}`);
	                }
	                const combinedConnections = [...existingNet.connections];
	                for (const newConn of netInfo.connections) {
	                    const netKey = `${newConn.moduleRef}\0${newConn.pinRef}`;
	                    if (!seen.has(netKey)) {
	                        seen.add(netKey);
	                        combinedConnections.push(newConn);
	                    }
	                }
	                projectNets.set(key, {
	                    ...existingNet,
	                    connections: combinedConnections,
	                });
	            }
	            else {
	                projectNets.set(key, netInfo);
	            }
	        }
	    }
	    const duplicatePinCatalog = buildDuplicatePinCatalog(projectModel, moduleMapping);
	    const netlistNets = new Map();
	    const rawPcadConnections = (0, pcadNetlistParser_1.getPcadNetlistRawConnections)(netlistSchematic);
	    const rejectedOracleRows = (0, pcadNetlistParser_1.getPcadRejectedConnectionRows)(netlistSchematic) ?? [];
	    const positiveAssertionsOnly = (0, pcadNetlistParser_1.usesPcadPositiveAssertionsOnly)(netlistSchematic);
	    const isPcadNetlist = netlistSchematic.sourceFile === 'pcad-netlist';
	    if (rawPcadConnections) {
	        for (const { moduleName, pinRef, netName } of rawPcadConnections) {
	            if (positiveAssertionsOnly && netName === types_1.NO_CONNECT_PLACEHOLDER_NET_NAME) {
	                continue;
	            }
	            const net = netlistNets.get(netName) ?? { name: netName, connections: [] };
	            net.connections.push({ moduleRef: moduleName, moduleName, pinRef });
	            netlistNets.set(netName, net);
	        }
	    }
	    else {
	        for (const [netName, netInfo] of Object.entries(netlistSchematic.nets)) {
	            netlistNets.set(netName, netInfo);
	        }
	    }
	    const rawNoConnectDeclarations = positiveAssertionsOnly ? undefined : netlistNets.get(types_1.NO_CONNECT_PLACEHOLDER_NET_NAME);
	    netlistNets.delete(types_1.NO_CONNECT_PLACEHOLDER_NET_NAME);
	    const noConnectDeclarations = getEffectiveNoConnectDeclarations(rawNoConnectDeclarations, netlistNets);
	    const oracleUnknownConnections = positiveAssertionsOnly ?
	        collectUnknownOracleConnections(projectNets, netlistNets, netlistModulesByName, moduleMapping, virtualModuleNames, duplicatePinCatalog)
	        : [];
	    const projectConnectionIndex = new Map();
	    for (const [projectNetName, projectNet] of projectNets) {
	        for (const connection of projectNet.connections) {
	            const oracleModuleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
	            projectConnectionIndex.set(buildConnectionSignature([{ moduleName: oracleModuleName, pinRef: connection.pinRef }], () => getComparablePinRef(connection, moduleMapping, duplicatePinCatalog)), [projectNetName, projectNet]);
	        }
	    }
	    const projectNormalizedIndex = buildNormalizedNetIndex(projectNets, opts);
	    const netlistNormalizedIndex = buildNormalizedNetIndex(netlistNets, opts);
	    const fullyNonCanonicalDuplicateNetNames = new Set();
	    for (const [netName, netInfo] of netlistNets) {
	        const hasRepeatedModelPinNumber = netInfo.connections.some((connection) => isRepeatedModelPinNumber(connection, moduleMapping, duplicatePinCatalog));
	        if (!hasRepeatedModelPinNumber && isFullyNonCanonicalDuplicateNet(netName, netInfo, netlistModulesByName)) {
	            fullyNonCanonicalDuplicateNetNames.add(netName);
	        }
	    }
	    const unmatchedProjectNets = [...projectNets].filter(([netName]) => !findMatchingNet(netName, netlistNets, opts, netlistNormalizedIndex));
	    const unmatchedNetlistNets = [...netlistNets].filter(([netName]) => !findMatchingNet(netName, projectNets, opts, projectNormalizedIndex) &&
	        !fullyNonCanonicalDuplicateNetNames.has(netName));
	    const unmatchedProjectByRealConnections = buildRealConnectionIndex(unmatchedProjectNets, moduleMapping, virtualModuleNames, (connection) => getComparablePinRef(connection, moduleMapping, duplicatePinCatalog));
	    const oracleModuleMapping = new Map();
	    const oracleVirtualModuleNames = new Set();
	    const unmatchedNetlistByRealConnections = buildRealConnectionIndex(unmatchedNetlistNets, oracleModuleMapping, oracleVirtualModuleNames);
	    const pairedProjectNetsByNetlistName = new Map();
	    for (const [signature, netlistMatches] of unmatchedNetlistByRealConnections) {
	        const projectMatches = unmatchedProjectByRealConnections.get(signature);
	        if (netlistMatches.length !== 1 || projectMatches?.length !== 1) {
	            continue;
	        }
	        const [netlistNetName] = netlistMatches[0];
	        const projectMatch = projectMatches[0];
	        pairedProjectNetsByNetlistName.set(netlistNetName, projectMatch);
	    }
	    const pairedProjectNetNames = new Set([...pairedProjectNetsByNetlistName.values()].map(([projectNetName]) => projectNetName));
	    let totalNetsChecked = 0;
	    let netNameMismatches = 0;
	    let missingConnections = 0;
	    let missingNets = 0;
	    let unexpectedNets = 0;
	    if (noConnectDeclarations) {
	        const declaredConnectionKeys = new Set(noConnectDeclarations.connections.map((declaration) => buildConnectionSignature([declaration])));
	        for (const declaration of noConnectDeclarations.connections) {
	            const projectNet = findProjectNetForOracleConnection(declaration, projectConnectionIndex);
	            if (projectNet) {
	                errors.push({
	                    type: types_1.NetlistValidationErrorType.MissingModuleConnection,
	                    message: `Netlist declares module '${declaration.moduleName}' pin '${declaration.pinRef}' as no-connect, but project model connects it to net '${projectNet.name}'`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                    projectNetName: projectNet.name,
	                    netlistNetName: types_1.NO_CONNECT_PLACEHOLDER_NET_NAME,
	                    moduleName: declaration.moduleName,
	                    pinRef: declaration.pinRef,
	                });
	                missingConnections++;
	                continue;
	            }
	        }
	        for (const [projectNetName, projectNet] of projectNets) {
	            if (projectNet.connections.length > 0 &&
	                projectNet.connections.every((connection) => declaredConnectionKeys.has(buildConnectionSignature([
	                    {
	                        moduleName: moduleMapping.get(connection.moduleName) ?? connection.moduleName,
	                        pinRef: connection.pinRef,
	                    },
	                ])))) {
	                projectNets.delete(projectNetName);
	            }
	        }
	    }
	    for (const [netlistNetName, netlistNet] of netlistNets) {
	        totalNetsChecked++;
	        if (fullyNonCanonicalDuplicateNetNames.has(netlistNetName)) {
	            continue;
	        }
	        let matchingProjectNet = findMatchingNet(netlistNetName, projectNets, opts, projectNormalizedIndex);
	        const matchedByName = matchingProjectNet !== null;
	        if (!matchingProjectNet) {
	            matchingProjectNet = pairedProjectNetsByNetlistName.get(netlistNetName) ?? null;
	        }
	        if (!matchingProjectNet) {
	            errors.push({
	                type: types_1.NetlistValidationErrorType.UnexpectedNetFound,
	                message: `Net '${netlistNetName}' found in netlist but not defined in project model`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                netlistNetName,
	                actualConnections: netlistNet.connections.map((conn) => ({
	                    moduleName: conn.moduleName,
	                    pinRef: conn.pinRef,
	                    ...(conn.pinFunction ? { pinFunction: conn.pinFunction } : {}),
	                })),
	            });
	            unexpectedNets++;
	            continue;
	        }
	        const [projectNetName, projectNet] = matchingProjectNet;
	        const isPcadLengthPlaceholder = isPcadNetlist &&
	            !matchedByName &&
	            isPcadTruncationPlaceholder(netlistNetName) &&
	            projectNetName.length > PCAD_MAX_NET_NAME_LENGTH;
	        if (netlistNetName !== projectNetName && !isPcadLengthPlaceholder) {
	            errors.push({
	                type: types_1.NetlistValidationErrorType.NetNameMismatch,
	                message: `Net name mismatch: netlist has '${netlistNetName}', project model expects '${projectNetName}'`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                projectNetName,
	                netlistNetName,
	                connections: projectNet.connections.map((conn) => ({
	                    moduleName: conn.moduleName,
	                    pinRef: conn.pinRef,
	                })),
	                actualConnections: netlistNet.connections.map((conn) => ({
	                    moduleName: conn.moduleName,
	                    pinRef: conn.pinRef,
	                    ...(conn.pinFunction ? { pinFunction: conn.pinFunction } : {}),
	                })),
	            });
	            netNameMismatches++;
	        }
	        const connectionErrors = validateProjectModelConnections(netlistNetName, netlistNet, projectNet, moduleMapping, virtualModuleNames, modulesByName, netlistModulesByName, projectModel, duplicatePinCatalog);
	        errors.push(...connectionErrors);
	        missingConnections += connectionErrors.length;
	    }
	    for (const [projectNetName, projectNet] of projectNets) {
	        const hasMatchingNetlistNet = Boolean(findMatchingNet(projectNetName, netlistNets, opts, netlistNormalizedIndex) ||
	            pairedProjectNetNames.has(projectNetName));
	        if (!hasMatchingNetlistNet) {
	            const allConnectionsToVirtualModules = projectNet.connections.every((connection) => virtualModuleNames.has(moduleMapping.get(connection.moduleName) ?? connection.moduleName));
	            if (allConnectionsToVirtualModules) {
	                continue;
	            }
	            errors.push({
	                type: types_1.NetlistValidationErrorType.UnexpectedProjectModelNet,
	                message: `Net '${projectNetName}' defined in project model but missing from netlist`,
	                severity: projectNet.connections.length > 1 ? sigma_engine_shared_1.BoundaryLevel.Error : sigma_engine_shared_1.BoundaryLevel.Recommendation,
	                projectNetName,
	                connections: projectNet.connections.map((conn) => ({
	                    moduleName: conn.moduleName,
	                    pinRef: conn.pinRef,
	                })),
	            });
	            missingNets++;
	        }
	    }
	    const result = {
	        type: 'NetlistValidation',
	        message: `Netlist validation completed with ${errors.length} issues found`,
	        severity: errors.length > 0 ? sigma_engine_shared_1.BoundaryLevel.Warning : sigma_engine_shared_1.BoundaryLevel.Recommendation,
	        errors,
	        summary: {
	            totalNetsChecked,
	            totalErrorsFound: errors.length,
	            netNameMismatches,
	            missingConnections,
	            missingNets,
	            unexpectedNets,
	            missingModules: errors.filter((e) => e.type === types_1.NetlistValidationErrorType.MissingModule).length,
	            oracleRejectedRows: { lineNumbers: rejectedOracleRows.map((row) => row.lineNumber) },
	            ...(positiveAssertionsOnly ? { oracleUnknownConnections: { connections: oracleUnknownConnections } } : {}),
	            unknownDesignators: summarizeUnknownDesignators(netlistNets, netlistModulesByName, collectModelDesignators(modulesByName, moduleMapping, projectNets)),
	        },
	    };
	    return result;
	}
	function findModelModulesAbsentFromNetlist(projectModel, netlistSchematic) {
	    const netlistDesignators = new Set(netlistSchematic.modules.map((mod) => mod.name));
	    const rawPcadConnections = (0, pcadNetlistParser_1.getPcadNetlistRawConnections)(netlistSchematic);
	    if (rawPcadConnections) {
	        for (const { moduleName } of rawPcadConnections) {
	            netlistDesignators.add(moduleName);
	        }
	    }
	    else {
	        for (const net of Object.values(netlistSchematic.nets)) {
	            for (const connection of net.connections) {
	                netlistDesignators.add(connection.moduleName);
	            }
	        }
	    }
	    const symbolNamesByModuleName = new Map();
	    for (const [symbolName, moduleName] of buildModuleMapping(projectModel)) {
	        symbolNamesByModuleName.set(moduleName, [...(symbolNamesByModuleName.get(moduleName) ?? []), symbolName]);
	    }
	    const absent = [];
	    for (const mod of projectModel.modules) {
	        if (projectModel.components[mod.componentRef]?.isVirtual === true) {
	            continue;
	        }
	        const names = [mod.name, ...(symbolNamesByModuleName.get(mod.name) ?? [])];
	        if (!names.some((name) => netlistDesignators.has(name))) {
	            absent.push(mod.name);
	        }
	    }
	    return absent.sort();
	}
	function collectModelDesignators(modulesByName, moduleMapping, projectNets) {
	    const designators = new Set([...modulesByName.keys(), ...moduleMapping.keys()]);
	    for (const net of projectNets.values()) {
	        for (const connection of net.connections) {
	            designators.add(connection.moduleName);
	        }
	    }
	    return designators;
	}
	function summarizeUnknownDesignators(netlistNets, netlistModulesByName, modelDesignators) {
	    const unknown = new Set();
	    for (const name of netlistModulesByName.keys()) {
	        if (!modelDesignators.has(name)) {
	            unknown.add(name);
	        }
	    }
	    for (const net of netlistNets.values()) {
	        for (const connection of net.connections) {
	            if (!modelDesignators.has(connection.moduleName)) {
	                unknown.add(connection.moduleName);
	            }
	        }
	    }
	    const modelBases = new Set();
	    for (const name of modelDesignators) {
	        modelBases.add(designatorBase(name));
	    }
	    let matchedInOtherForm = 0;
	    for (const designator of unknown) {
	        if (modelBases.has(designatorBase(designator))) {
	            matchedInOtherForm++;
	        }
	    }
	    return { count: unknown.size, matchedInOtherForm };
	}
	function designatorBase(designator) {
	    return /^(.*?\d)[._]/.exec(designator)?.[1] ?? designator;
	}
	function collectUnknownOracleConnections(projectNets, netlistNets, netlistModulesByName, moduleMapping, virtualModuleNames, duplicatePinCatalog) {
	    const positiveKeys = new Set();
	    for (const net of netlistNets.values()) {
	        for (const connection of net.connections) {
	            positiveKeys.add(buildConnectionSignature([connection]));
	        }
	    }
	    const unknown = new Map();
	    for (const net of projectNets.values()) {
	        for (const connection of net.connections) {
	            const moduleName = moduleMapping.get(connection.moduleName) ?? connection.moduleName;
	            if (virtualModuleNames.has(moduleName)) {
	                continue;
	            }
	            if (!netlistModulesByName.has(moduleName)) {
	                continue;
	            }
	            const candidate = { moduleName, pinRef: connection.pinRef };
	            const key = buildConnectionSignature([candidate], () => getComparablePinRef(connection, moduleMapping, duplicatePinCatalog));
	            if (!positiveKeys.has(key)) {
	                unknown.set(key, candidate);
	            }
	        }
	    }
	    return [...unknown.values()].sort((left, right) => {
	        const moduleOrder = left.moduleName.localeCompare(right.moduleName);
	        return moduleOrder !== 0 ? moduleOrder : left.pinRef.localeCompare(right.pinRef);
	    });
	}
	function getOracleRejectedRowLineNumbers(result) {
	    return result.summary.oracleRejectedRows?.lineNumbers ?? [];
	}
	function findMatchingNet(netName, targetNets, options, normalizedIndex) {
	    const exactMatch = targetNets.get(netName);
	    if (exactMatch) {
	        return [netName, exactMatch];
	    }
	    if (options.ignoreCase || options.ignoreWhitespace) {
	        const normalized = normalizeNetName(netName, options);
	        const fuzzyMatch = normalizedIndex.get(normalized);
	        if (fuzzyMatch) {
	            return fuzzyMatch;
	        }
	    }
	    return null;
	}
	function isFullyNonCanonicalDuplicateNet(netlistNetName, netlistNet, netlistModulesByName) {
	    if (netlistNet.connections.length === 0) {
	        return false;
	    }
	    return netlistNet.connections.every((connection) => {
	        const module = netlistModulesByName.get(connection.moduleName);
	        const canonicalNetName = module ? findNetlistConnection(connection.pinRef, module) : undefined;
	        return canonicalNetName !== undefined && canonicalNetName !== netlistNetName;
	    });
	}
	function validateProjectModelConnections(netlistNetName, netlistNet, projectNet, moduleMapping, virtualModuleNames, modulesByName, netlistModulesByName, projectModel, duplicatePinCatalog) {
	    const errors = [];
	    const hasRepeatedModelPinNumber = netlistNet.connections.some((connection) => isRepeatedModelPinNumber(connection, moduleMapping, duplicatePinCatalog));
	    const isNonCanonicalDuplicateNet = !hasRepeatedModelPinNumber &&
	        netlistNet.connections.some((connection) => {
	            const module = netlistModulesByName.get(connection.moduleName);
	            const canonicalNetName = module ? findNetlistConnection(connection.pinRef, module) : undefined;
	            return canonicalNetName !== undefined && canonicalNetName !== netlistNetName;
	        });
	    for (const expectedConn of projectNet.connections) {
	        const physicalModuleName = moduleMapping.get(expectedConn.moduleName) ?? expectedConn.moduleName;
	        if (virtualModuleNames.has(physicalModuleName)) {
	            continue;
	        }
	        let connectionExistsInNetlist = false;
	        for (const c of netlistNet.connections) {
	            if ((c.moduleName === expectedConn.moduleName || c.moduleName === physicalModuleName) &&
	                connectionsMatch(expectedConn, c, moduleMapping, duplicatePinCatalog)) {
	                connectionExistsInNetlist = true;
	                break;
	            }
	        }
	        if (!connectionExistsInNetlist && !isNonCanonicalDuplicateNet) {
	            const netlistModule = netlistModulesByName.get(expectedConn.moduleName);
	            const netlistConnection = netlistModule ? findNetlistConnection(expectedConn.pinRef, netlistModule) : undefined;
	            errors.push({
	                type: types_1.NetlistValidationErrorType.MissingModuleConnection,
	                message: `Project model has module '${expectedConn.moduleName}' pin '${getComparablePinRef(expectedConn, moduleMapping, duplicatePinCatalog)}' connected to net '${netlistNetName}', but netlist shows it connected to net '${netlistConnection}'`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Error,
	                projectNetName: projectNet.name,
	                netlistNetName: netlistConnection,
	                moduleName: expectedConn.moduleName,
	                pinRef: getComparablePinRef(expectedConn, moduleMapping, duplicatePinCatalog),
	            });
	        }
	    }
	    for (const actualConn of netlistNet.connections) {
	        if (virtualModuleNames.has(actualConn.moduleName)) {
	            continue;
	        }
	        let connectionExpectedByProject = false;
	        for (const c of projectNet.connections) {
	            if (virtualModuleNames.has(c.moduleName)) {
	                continue;
	            }
	            const physicalModuleName = moduleMapping.get(c.moduleName) ?? c.moduleName;
	            if ((c.moduleName === actualConn.moduleName || physicalModuleName === actualConn.moduleName) &&
	                connectionsMatch(c, actualConn, moduleMapping, duplicatePinCatalog)) {
	                connectionExpectedByProject = true;
	                break;
	            }
	        }
	        if (!connectionExpectedByProject) {
	            const projectModule = modulesByName.get(actualConn.moduleName);
	            if (!projectModule) {
	                errors.push({
	                    type: types_1.NetlistValidationErrorType.MissingModule,
	                    message: `Module '${actualConn.moduleName}' not found in project model`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    netlistNetName,
	                    moduleName: actualConn.moduleName,
	                    pinRef: actualConn.pinRef,
	                });
	                continue;
	            }
	            let connection;
	            for (const instance of projectModule.instances) {
	                if (projectModel.designs[instance.designId].modelType !== 'schematic') {
	                    continue;
	                }
	                const design = projectModel.designs[instance.designId];
	                let designModule;
	                for (const m of design.modules) {
	                    if (m.ref === instance.ref) {
	                        designModule = m;
	                        break;
	                    }
	                }
	                if (designModule && findProjectConnection(actualConn.pinRef, designModule) !== undefined) {
	                    connection = instance;
	                    break;
	                }
	            }
	            if (!connection) {
	                errors.push({
	                    type: types_1.NetlistValidationErrorType.UnexpectedConnection,
	                    message: `Netlist shows module '${actualConn.moduleName}' pin '${actualConn.pinRef}' connected to net '${netlistNetName}', but project model has it unconnected`,
	                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                    netlistNetName,
	                    moduleName: actualConn.moduleName,
	                    pinRef: actualConn.pinRef,
	                });
	                continue;
	            }
	            const design = projectModel.designs[connection.designId];
	            let designModule;
	            for (const m of design.modules) {
	                if (m.ref === connection.ref) {
	                    designModule = m;
	                    break;
	                }
	            }
	            if (!designModule) {
	                throw new Error(`Module ${connection.ref} not found in design ${connection.designId}`);
	            }
	            const projectConnection = findProjectConnection(actualConn.pinRef, designModule);
	            errors.push({
	                type: types_1.NetlistValidationErrorType.UnexpectedConnection,
	                message: `Netlist shows module '${actualConn.moduleName}' pin '${actualConn.pinRef}' connected to net '${netlistNetName}', but project model has it connected to net '${projectConnection}'`,
	                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
	                projectNetName: projectConnection,
	                netlistNetName,
	                moduleName: actualConn.moduleName,
	                pinRef: actualConn.pinRef,
	            });
	        }
	    }
	    return errors;
	}
	function buildModuleMapping(projectModel) {
	    const refToModuleName = new Map();
	    const moduleNameToPhysicalName = new Map();
	    const sourceReferenceModuleNames = new Set();
	    for (const design of Object.values(projectModel.designs)) {
	        if (design.modelType === 'schematic') {
	            for (const module of design.modules) {
	                refToModuleName.set(module.ref, module.name);
	                const sourceReference = module.metadata?.['kicad.sourceReference'];
	                if (typeof sourceReference === 'string' &&
	                    sourceReference.length > 0 &&
	                    module.name.startsWith(sourceReference) &&
	                    module.name.length > sourceReference.length) {
	                    moduleNameToPhysicalName.set(module.name, sourceReference);
	                    sourceReferenceModuleNames.add(module.name);
	                }
	            }
	        }
	    }
	    for (const projectModule of projectModel.modules) {
	        for (const instance of projectModule.instances) {
	            const schematicSymbolName = refToModuleName.get(instance.ref);
	            if (schematicSymbolName && !sourceReferenceModuleNames.has(schematicSymbolName)) {
	                moduleNameToPhysicalName.set(schematicSymbolName, projectModule.name);
	            }
	        }
	    }
	    return moduleNameToPhysicalName;
	}
	
	return netlistValidation;
}

var remediation = {};

var hasRequiredRemediation;

function requireRemediation () {
	if (hasRequiredRemediation) return remediation;
	hasRequiredRemediation = 1;
	Object.defineProperty(remediation, "__esModule", { value: true });
	remediation.NetlistValidationRemediation = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const netlist_gate_1 = requireNetlistGate();
	const remediation_1 = requireRemediation$1();
	class NetlistValidationRemediation {
	    static { this.MIN_UNKNOWN_DESIGNATORS = 10; }
	    static { this.DECISIVE_SHARE = 0.8; }
	    static forResult(result, netlistSource) {
	        const rejectedRows = result.summary.oracleRejectedRows?.lineNumbers.length ?? 0;
	        if (rejectedRows > 0) {
	            return {
	                owner: remediation_1.RemediationOwner.Dev,
	                summary: `Our netlist reader could not represent ${rejectedRows} ${rejectedRows === 1 ? 'row' : 'rows'} of this netlist, so the comparison is incomplete.`,
	                steps: [
	                    'Escalate to dev with the build id: this is a limitation in our netlist reader, not a fault in the netlist.',
	                ],
	            };
	        }
	        const hasErrors = result.errors.some((error) => error.severity === undefined || error.severity === sigma_engine_shared_1.BoundaryLevel.Error);
	        if (hasErrors && netlistSource === netlist_gate_1.NetlistSource.Generated) {
	            return {
	                owner: remediation_1.RemediationOwner.Dev,
	                summary: "The project model disagrees with a netlist the CAD tool exported itself, which most likely points to a bug in Cadstrom's parser.",
	                steps: [
	                    'Escalate to dev with the build id and the findings below.',
	                    'Accept the errors only if dev confirms the analysis can proceed on this model.',
	                ],
	            };
	        }
	        const unknownDesignators = result.summary.unknownDesignators;
	        if (unknownDesignators !== undefined &&
	            unknownDesignators.count >= NetlistValidationRemediation.MIN_UNKNOWN_DESIGNATORS) {
	            const { count, matchedInOtherForm } = unknownDesignators;
	            const unmatched = count - matchedInOtherForm;
	            if (matchedInOtherForm >= count * NetlistValidationRemediation.DECISIVE_SHARE) {
	                return {
	                    owner: remediation_1.RemediationOwner.Dev,
	                    summary: `${matchedInOtherForm} of the ${count} designators the project model lacks are in it under another form, so the netlist matches the design and the mismatch most likely points to a bug in Cadstrom's parser.`,
	                    steps: [
	                        'Do not re-upload the netlist: a replacement will not change the result.',
	                        'Escalate to dev with the build id and the findings below.',
	                        'Accept the errors only if dev confirms the analysis can proceed on this model.',
	                    ],
	                };
	            }
	            if (netlistSource === netlist_gate_1.NetlistSource.Uploaded &&
	                unmatched >= count * NetlistValidationRemediation.DECISIVE_SHARE) {
	                return {
	                    owner: remediation_1.RemediationOwner.Ops,
	                    summary: `The netlist names ${unmatched} parts the project model does not have in any form, so it is most likely from another design or board, or the upload is missing schematic sheets.`,
	                    steps: [
	                        "Check that the netlist was exported from this board's project.",
	                        'Check that the uploaded CAD files include every schematic sheet of the design.',
	                        'Upload the right netlist from the netlist column, or re-upload the complete design.',
	                        'If both are right, escalate to dev as a possible parser bug.',
	                    ],
	                };
	            }
	        }
	        if (!hasErrors) {
	            return {
	                owner: remediation_1.RemediationOwner.Ops,
	                summary: 'Validation found only warnings against the netlist.',
	                steps: [
	                    'Review the findings below.',
	                    'If they are cosmetic (for example, net names that differ only in form), accept them with a reason.',
	                    'Otherwise, escalate to dev with the build id.',
	                ],
	            };
	        }
	        if (netlistSource === undefined) {
	            return {
	                owner: remediation_1.RemediationOwner.Ops,
	                summary: 'The project model disagrees with the netlist, and where that netlist came from was not recorded.',
	                steps: [
	                    'Check the build files for which netlist was validated: an upload, or one the generator produced.',
	                    'If it was uploaded and is stale or from another design, upload a replacement from the netlist column.',
	                    'If it was generated, or matches the CAD files, escalate to dev as a possible parser bug.',
	                ],
	            };
	        }
	        return {
	            owner: remediation_1.RemediationOwner.Ops,
	            summary: 'The project model disagrees with the netlist that was supplied for this build.',
	            steps: [
	                'Check that the netlist was exported from the same revision of the design as the uploaded CAD files.',
	                'If it is stale or from another design, upload a replacement from the netlist column.',
	                'If it matches the CAD files, escalate to dev as a possible parser bug.',
	            ],
	        };
	    }
	}
	remediation.NetlistValidationRemediation = NetlistValidationRemediation;
	
	return remediation;
}

var hasRequiredNetlistValidation;

function requireNetlistValidation () {
	if (hasRequiredNetlistValidation) return NetlistValidation;
	hasRequiredNetlistValidation = 1;
	(function (exports) {
		var __createBinding = (NetlistValidation && NetlistValidation.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (NetlistValidation && NetlistValidation.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireNetlistValidation$1(), exports);
		__exportStar(requireTypes$1(), exports);
		__exportStar(requireRemediation(), exports);
		
	} (NetlistValidation));
	return NetlistValidation;
}

var DataScience = {};

var modelCards = {};

var anthropicModelCards = {};

var modelCard = {};

var hasRequiredModelCard;

function requireModelCard$1 () {
	if (hasRequiredModelCard) return modelCard;
	hasRequiredModelCard = 1;
	Object.defineProperty(modelCard, "__esModule", { value: true });
	modelCard.AiModelEffort = modelCard.AiModelProvider = void 0;
	var AiModelProvider;
	(function (AiModelProvider) {
	    AiModelProvider["openAi"] = "openai";
	    AiModelProvider["google"] = "google";
	    AiModelProvider["anthropic"] = "anthropic";
	})(AiModelProvider || (modelCard.AiModelProvider = AiModelProvider = {}));
	var AiModelEffort;
	(function (AiModelEffort) {
	    AiModelEffort["none"] = "none";
	    AiModelEffort["minimal"] = "minimal";
	    AiModelEffort["low"] = "low";
	    AiModelEffort["medium"] = "medium";
	    AiModelEffort["high"] = "high";
	    AiModelEffort["xhigh"] = "xhigh";
	    AiModelEffort["max"] = "max";
	})(AiModelEffort || (modelCard.AiModelEffort = AiModelEffort = {}));
	
	return modelCard;
}

var hasRequiredAnthropicModelCards;

function requireAnthropicModelCards () {
	if (hasRequiredAnthropicModelCards) return anthropicModelCards;
	hasRequiredAnthropicModelCards = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.anthropicModelCards = exports.ANTHROPIC_MODEL_CARDS = exports.AnthropicThinkingMode = exports.AnthropicThinkingType = exports.AnthropicAiModel = void 0;
		exports.getAnthropicModelCard = getAnthropicModelCard;
		exports.supportsAnthropicReasoningValue = supportsAnthropicReasoningValue;
		const model_card_1 = requireModelCard$1();
		var AnthropicAiModel;
		(function (AnthropicAiModel) {
		    AnthropicAiModel["claude_opus_4_8"] = "claude-opus-4-8";
		    AnthropicAiModel["claude_opus_4_6"] = "claude-opus-4-6";
		    AnthropicAiModel["claude_sonnet_5"] = "claude-sonnet-5";
		    AnthropicAiModel["claude_sonnet_4_6"] = "claude-sonnet-4-6";
		    AnthropicAiModel["claude_haiku_4_5"] = "claude-haiku-4-5";
		})(AnthropicAiModel || (exports.AnthropicAiModel = AnthropicAiModel = {}));
		var AnthropicThinkingType;
		(function (AnthropicThinkingType) {
		    AnthropicThinkingType["disabled"] = "disabled";
		    AnthropicThinkingType["enabled"] = "enabled";
		})(AnthropicThinkingType || (exports.AnthropicThinkingType = AnthropicThinkingType = {}));
		var AnthropicThinkingMode;
		(function (AnthropicThinkingMode) {
		    AnthropicThinkingMode["adaptive"] = "adaptive";
		    AnthropicThinkingMode["manual"] = "manual";
		})(AnthropicThinkingMode || (exports.AnthropicThinkingMode = AnthropicThinkingMode = {}));
		function effort({ default: defaultValue }, ...supportedValues) {
		    return {
		        parameter: 'effort',
		        supportedValues,
		        defaultValue,
		    };
		}
		function pricing(input, output) {
		    return {
		        currency: 'USD',
		        unit: 'perMillionTokens',
		        input,
		        output,
		    };
		}
		const ADAPTIVE_THINKING = {
		    mode: AnthropicThinkingMode.adaptive,
		    enabledByDefault: false,
		    manualBudgetTokens: 'unsupported',
		};
		const ADAPTIVE_THINKING_WITH_DEPRECATED_MANUAL_BUDGET = {
		    mode: AnthropicThinkingMode.adaptive,
		    enabledByDefault: false,
		    manualBudgetTokens: 'deprecated',
		};
		exports.ANTHROPIC_MODEL_CARDS = {
		    [AnthropicAiModel.claude_opus_4_8]: {
		        model: AnthropicAiModel.claude_opus_4_8,
		        name: 'Claude Opus 4.8',
		        provider: model_card_1.AiModelProvider.anthropic,
		        reasoning: effort({ default: model_card_1.AiModelEffort.high }, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh, model_card_1.AiModelEffort.max),
		        thinking: ADAPTIVE_THINKING,
		        standardTokenPricing: pricing(5, 25),
		        contextWindowTokens: 1_000_000,
		        maxOutputTokens: 128_000,
		    },
		    [AnthropicAiModel.claude_opus_4_6]: {
		        model: AnthropicAiModel.claude_opus_4_6,
		        name: 'Claude Opus 4.6',
		        provider: model_card_1.AiModelProvider.anthropic,
		        reasoning: effort({ default: model_card_1.AiModelEffort.high }, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.max),
		        thinking: ADAPTIVE_THINKING_WITH_DEPRECATED_MANUAL_BUDGET,
		        standardTokenPricing: pricing(5, 25),
		        contextWindowTokens: 1_000_000,
		        maxOutputTokens: 128_000,
		    },
		    [AnthropicAiModel.claude_sonnet_5]: {
		        model: AnthropicAiModel.claude_sonnet_5,
		        name: 'Claude Sonnet 5',
		        provider: model_card_1.AiModelProvider.anthropic,
		        reasoning: effort({ default: model_card_1.AiModelEffort.high }, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh, model_card_1.AiModelEffort.max),
		        thinking: {
		            ...ADAPTIVE_THINKING,
		            enabledByDefault: true,
		        },
		        standardTokenPricing: pricing(3, 15),
		        promotionalTokenPricing: {
		            pricing: pricing(2, 10),
		            through: '2026-08-31',
		        },
		        contextWindowTokens: 1_000_000,
		        maxOutputTokens: 128_000,
		    },
		    [AnthropicAiModel.claude_sonnet_4_6]: {
		        model: AnthropicAiModel.claude_sonnet_4_6,
		        name: 'Claude Sonnet 4.6',
		        provider: model_card_1.AiModelProvider.anthropic,
		        reasoning: effort({ default: model_card_1.AiModelEffort.high }, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.max),
		        thinking: ADAPTIVE_THINKING_WITH_DEPRECATED_MANUAL_BUDGET,
		        standardTokenPricing: pricing(3, 15),
		        contextWindowTokens: 1_000_000,
		        maxOutputTokens: 128_000,
		    },
		    [AnthropicAiModel.claude_haiku_4_5]: {
		        model: AnthropicAiModel.claude_haiku_4_5,
		        name: 'Claude Haiku 4.5',
		        provider: model_card_1.AiModelProvider.anthropic,
		        reasoning: {
		            parameter: 'thinking.type',
		            supportedValues: [AnthropicThinkingType.disabled, AnthropicThinkingType.enabled],
		            defaultValue: AnthropicThinkingType.disabled,
		        },
		        thinking: {
		            mode: AnthropicThinkingMode.manual,
		            enabledByDefault: false,
		            manualBudgetTokens: 'supported',
		        },
		        standardTokenPricing: pricing(1, 5),
		        contextWindowTokens: 200_000,
		        maxOutputTokens: 64_000,
		    },
		};
		exports.anthropicModelCards = Object.values(exports.ANTHROPIC_MODEL_CARDS);
		function getAnthropicModelCard(model) {
		    return exports.ANTHROPIC_MODEL_CARDS[model];
		}
		function supportsAnthropicReasoningValue(model, value) {
		    return getAnthropicModelCard(model).reasoning?.supportedValues.includes(value) ?? false;
		}
		
	} (anthropicModelCards));
	return anthropicModelCards;
}

var modelDefaults = {};

var hasRequiredModelDefaults;

function requireModelDefaults () {
	if (hasRequiredModelDefaults) return modelDefaults;
	hasRequiredModelDefaults = 1;
	Object.defineProperty(modelDefaults, "__esModule", { value: true });
	modelDefaults.isAiModelEffort = isAiModelEffort;
	modelDefaults.modelDefaults = modelDefaults$1;
	const model_card_1 = requireModelCard$1();
	const REASONING_EFFORT_ORDER = [
	    model_card_1.AiModelEffort.none,
	    model_card_1.AiModelEffort.minimal,
	    model_card_1.AiModelEffort.low,
	    model_card_1.AiModelEffort.medium,
	    model_card_1.AiModelEffort.high,
	    model_card_1.AiModelEffort.xhigh,
	    model_card_1.AiModelEffort.max,
	];
	function isAiModelEffort(value) {
	    return REASONING_EFFORT_ORDER.includes(value);
	}
	function coerceReasoningValue(parameters, requestedValue) {
	    if (requestedValue === undefined || parameters.supportedValues.includes(requestedValue)) {
	        return requestedValue ?? parameters.defaultValue;
	    }
	    if (!isAiModelEffort(requestedValue)) {
	        return parameters.defaultValue;
	    }
	    const requestedIndex = REASONING_EFFORT_ORDER.indexOf(requestedValue);
	    const supportedEfforts = parameters.supportedValues.filter(isAiModelEffort);
	    return (supportedEfforts.reduce((closest, candidate) => {
	        if (!closest) {
	            return candidate;
	        }
	        const candidateIndex = REASONING_EFFORT_ORDER.indexOf(candidate);
	        const closestIndex = REASONING_EFFORT_ORDER.indexOf(closest);
	        const candidateDistance = Math.abs(candidateIndex - requestedIndex);
	        const closestDistance = Math.abs(closestIndex - requestedIndex);
	        return (candidateDistance < closestDistance ||
	            (candidateDistance === closestDistance && candidateIndex < closestIndex)) ?
	            candidate
	            : closest;
	    }, undefined) ?? parameters.defaultValue);
	}
	function modelDefaults$1(card, overrides) {
	    const maxResponseTokens = validateMaxResponseTokens(card, overrides?.maxResponseTokens);
	    const cap = maxResponseTokens === undefined ? {} : { maxResponseTokens };
	    if (!card.reasoning) {
	        return { model: card.model, ...cap };
	    }
	    return {
	        model: card.model,
	        reasoning: {
	            parameter: card.reasoning.parameter,
	            value: coerceReasoningValue(card.reasoning, overrides?.reasoning),
	        },
	        ...cap,
	    };
	}
	function validateMaxResponseTokens(card, requested) {
	    if (requested === undefined) {
	        return undefined;
	    }
	    if (!Number.isInteger(requested) || requested <= 0) {
	        throw new Error(`ModelDefaults_InvalidMaxResponseTokens:${card.model}:${requested}`);
	    }
	    if (requested > card.maxOutputTokens) {
	        throw new Error(`ModelDefaults_MaxResponseTokensExceedsCard:${card.model}:${requested}>${card.maxOutputTokens}`);
	    }
	    return requested;
	}
	
	return modelDefaults;
}

var openAiModelCards = {};

var hasRequiredOpenAiModelCards;

function requireOpenAiModelCards () {
	if (hasRequiredOpenAiModelCards) return openAiModelCards;
	hasRequiredOpenAiModelCards = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.openAiModelCards = exports.OPEN_AI_MODEL_CARDS = exports.OpenAiModel = void 0;
		exports.getOpenAiModelCard = getOpenAiModelCard;
		exports.supportsOpenAiReasoningEffort = supportsOpenAiReasoningEffort;
		const model_card_1 = requireModelCard$1();
		var OpenAiModel;
		(function (OpenAiModel) {
		    OpenAiModel["gpt_5_6_sol"] = "gpt-5.6-sol";
		    OpenAiModel["gpt_5_6_terra"] = "gpt-5.6-terra";
		    OpenAiModel["gpt_5_6_luna"] = "gpt-5.6-luna";
		    OpenAiModel["gpt_5_4"] = "gpt-5.4";
		    OpenAiModel["gpt_5_2"] = "gpt-5.2";
		    OpenAiModel["gpt_5_1"] = "gpt-5.1";
		    OpenAiModel["gpt_5_mini"] = "gpt-5-mini";
		    OpenAiModel["gpt_5_nano"] = "gpt-5-nano";
		    OpenAiModel["gpt_4_1"] = "gpt-4.1";
		})(OpenAiModel || (exports.OpenAiModel = OpenAiModel = {}));
		const REASONING_EFFORT_PARAMETER = 'reasoning.effort';
		function reasoning({ default: defaultValue }, ...supportedValues) {
		    return {
		        parameter: REASONING_EFFORT_PARAMETER,
		        supportedValues,
		        defaultValue,
		    };
		}
		function pricing(input, output) {
		    return {
		        currency: 'USD',
		        unit: 'perMillionTokens',
		        input,
		        output,
		    };
		}
		exports.OPEN_AI_MODEL_CARDS = {
		    [OpenAiModel.gpt_5_6_sol]: {
		        model: OpenAiModel.gpt_5_6_sol,
		        name: 'GPT-5.6 Sol',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.none, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh, model_card_1.AiModelEffort.max),
		        standardTokenPricing: pricing(5, 30),
		        contextWindowTokens: 1_050_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_6_terra]: {
		        model: OpenAiModel.gpt_5_6_terra,
		        name: 'GPT-5.6 Terra',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.none, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh, model_card_1.AiModelEffort.max),
		        standardTokenPricing: pricing(2.5, 15),
		        contextWindowTokens: 1_050_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_6_luna]: {
		        model: OpenAiModel.gpt_5_6_luna,
		        name: 'GPT-5.6 Luna',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.none, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh, model_card_1.AiModelEffort.max),
		        standardTokenPricing: pricing(1, 6),
		        contextWindowTokens: 1_050_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_4]: {
		        model: OpenAiModel.gpt_5_4,
		        name: 'GPT-5.4',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.none, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh),
		        standardTokenPricing: pricing(2.5, 15),
		        contextWindowTokens: 1_050_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_2]: {
		        model: OpenAiModel.gpt_5_2,
		        name: 'GPT-5.2',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.none, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high, model_card_1.AiModelEffort.xhigh),
		        standardTokenPricing: pricing(1.75, 14),
		        contextWindowTokens: 400_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_1]: {
		        model: OpenAiModel.gpt_5_1,
		        name: 'GPT-5.1',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.none, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high),
		        standardTokenPricing: pricing(1.25, 10),
		        contextWindowTokens: 400_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_mini]: {
		        model: OpenAiModel.gpt_5_mini,
		        name: 'GPT-5 mini',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.minimal, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high),
		        standardTokenPricing: pricing(0.25, 2),
		        contextWindowTokens: 400_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_5_nano]: {
		        model: OpenAiModel.gpt_5_nano,
		        name: 'GPT-5 nano',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: reasoning({ default: model_card_1.AiModelEffort.medium }, model_card_1.AiModelEffort.minimal, model_card_1.AiModelEffort.low, model_card_1.AiModelEffort.medium, model_card_1.AiModelEffort.high),
		        standardTokenPricing: pricing(0.05, 0.4),
		        contextWindowTokens: 400_000,
		        maxOutputTokens: 128_000,
		    },
		    [OpenAiModel.gpt_4_1]: {
		        model: OpenAiModel.gpt_4_1,
		        name: 'GPT-4.1',
		        provider: model_card_1.AiModelProvider.openAi,
		        reasoning: null,
		        standardTokenPricing: pricing(2, 8),
		        contextWindowTokens: 1_047_576,
		        maxOutputTokens: 32_768,
		    },
		};
		exports.openAiModelCards = Object.values(exports.OPEN_AI_MODEL_CARDS);
		function getOpenAiModelCard(model) {
		    return exports.OPEN_AI_MODEL_CARDS[model];
		}
		function supportsOpenAiReasoningEffort(model, effort) {
		    return getOpenAiModelCard(model).reasoning?.supportedValues.includes(effort) ?? false;
		}
		
	} (openAiModelCards));
	return openAiModelCards;
}

var requireModelCard = {};

var hasRequiredRequireModelCard;

function requireRequireModelCard () {
	if (hasRequiredRequireModelCard) return requireModelCard;
	hasRequiredRequireModelCard = 1;
	Object.defineProperty(requireModelCard, "__esModule", { value: true });
	requireModelCard.requireModelCard = requireModelCard$1;
	const anthropic_model_cards_1 = requireAnthropicModelCards();
	const open_ai_model_cards_1 = requireOpenAiModelCards();
	function requireModelCard$1(model) {
	    const card = [...anthropic_model_cards_1.anthropicModelCards, ...open_ai_model_cards_1.openAiModelCards].find((candidate) => String(candidate.model) === model);
	    if (card === undefined) {
	        throw new Error(`RequireModelCard_UnsupportedModel:${model}`);
	    }
	    return card;
	}
	
	return requireModelCard;
}

var hasRequiredModelCards;

function requireModelCards () {
	if (hasRequiredModelCards) return modelCards;
	hasRequiredModelCards = 1;
	(function (exports) {
		var __createBinding = (modelCards && modelCards.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (modelCards && modelCards.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAnthropicModelCards(), exports);
		__exportStar(requireModelCard$1(), exports);
		__exportStar(requireModelDefaults(), exports);
		__exportStar(requireOpenAiModelCards(), exports);
		__exportStar(requireRequireModelCard(), exports);
		
	} (modelCards));
	return modelCards;
}

var aiModelConfig = {};

var hasRequiredAiModelConfig;

function requireAiModelConfig () {
	if (hasRequiredAiModelConfig) return aiModelConfig;
	hasRequiredAiModelConfig = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AiModelConfigSchema = exports.AiReasoningSummarySchema = exports.AiReasoningSummary = exports.AiModelEffortSchema = exports.AiModelSchema = exports.AnthropicAiModelSchema = exports.GoogleGenAiModelSchema = exports.GoogleGenAiModel = exports.OpenAiModelSchema = void 0;
		exports.getModelEnum = getModelEnum;
		exports.supportsReasoningEffortParameter = supportsReasoningEffortParameter;
		const zod_1 = /*@__PURE__*/ requireZod();
		const model_cards_1 = requireModelCards();
		exports.OpenAiModelSchema = zod_1.z.nativeEnum(model_cards_1.OpenAiModel);
		var GoogleGenAiModel;
		(function (GoogleGenAiModel) {
		    GoogleGenAiModel["gemini_3_1_pro_preview"] = "gemini-3.1-pro-preview";
		    GoogleGenAiModel["gemini_2_5_pro"] = "gemini-2.5-pro";
		    GoogleGenAiModel["gemini_2_5_flash"] = "gemini-2.5-flash";
		    GoogleGenAiModel["gemini_2_5_flash_lite"] = "gemini-2.5-flash-lite";
		})(GoogleGenAiModel || (exports.GoogleGenAiModel = GoogleGenAiModel = {}));
		exports.GoogleGenAiModelSchema = zod_1.z.nativeEnum(GoogleGenAiModel);
		exports.AnthropicAiModelSchema = zod_1.z.nativeEnum(model_cards_1.AnthropicAiModel);
		exports.AiModelSchema = zod_1.z.union([
		    exports.OpenAiModelSchema,
		    exports.GoogleGenAiModelSchema,
		    exports.AnthropicAiModelSchema,
		]);
		exports.AiModelEffortSchema = zod_1.z.nativeEnum(model_cards_1.AiModelEffort);
		var AiReasoningSummary;
		(function (AiReasoningSummary) {
		    AiReasoningSummary["concise"] = "concise";
		    AiReasoningSummary["detailed"] = "detailed";
		})(AiReasoningSummary || (exports.AiReasoningSummary = AiReasoningSummary = {}));
		exports.AiReasoningSummarySchema = zod_1.z.nativeEnum(AiReasoningSummary);
		exports.AiModelConfigSchema = zod_1.z.object({
		    model: exports.AiModelSchema,
		    temperature: zod_1.z.number().optional(),
		    topP: zod_1.z.number().optional(),
		    effort: exports.AiModelEffortSchema.optional(),
		    reasoningSummary: exports.AiReasoningSummarySchema.optional(),
		    maxOutputTokens: zod_1.z.number().optional(),
		});
		function getModelEnum(model) {
		    if (Object.values(model_cards_1.OpenAiModel).includes(model)) {
		        return model_cards_1.OpenAiModel;
		    }
		    if (Object.values(GoogleGenAiModel).includes(model)) {
		        return GoogleGenAiModel;
		    }
		    if (Object.values(model_cards_1.AnthropicAiModel).includes(model)) {
		        return model_cards_1.AnthropicAiModel;
		    }
		    return null;
		}
		function supportsReasoningEffortParameter(model) {
		    if (!Object.values(model_cards_1.OpenAiModel).includes(model)) {
		        return false;
		    }
		    return (0, model_cards_1.getOpenAiModelCard)(model).reasoning?.parameter === 'reasoning.effort';
		}
		
	} (aiModelConfig));
	return aiModelConfig;
}

var signalTiesDenormalized = {};

var hasRequiredSignalTiesDenormalized;

function requireSignalTiesDenormalized () {
	if (hasRequiredSignalTiesDenormalized) return signalTiesDenormalized;
	hasRequiredSignalTiesDenormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SignalTiesDenormalizedSchema = exports.ExtractedSignalTieSchema = exports.ComponentValueTypeSchema = exports.ComponentValueType = exports.SignalTieApplicabilitySchema = exports.SignalTieApplicability = void 0;
		exports.getDenormalizedSignalTiesFromSignals = getDenormalizedSignalTiesFromSignals;
		exports.applyDenormalizedSignalTiesToSignals = applyDenormalizedSignalTiesToSignals;
		exports.normalizeComponentValue = normalizeComponentValue;
		exports.convertZeroComponentValueToNull = convertZeroComponentValueToNull;
		exports.convertZeroValuesToNullInSignalTies = convertZeroValuesToNullInSignalTies;
		exports.normalizeAndDeduplicateSignalTies = normalizeAndDeduplicateSignalTies;
		exports.deduplicateSignalTies = deduplicateSignalTies;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		const SignalTies_1 = requireSignalTies();
		var SignalTieApplicability;
		(function (SignalTieApplicability) {
		    SignalTieApplicability["Required"] = "Required";
		    SignalTieApplicability["Recommended"] = "Recommended";
		    SignalTieApplicability["Conditional"] = "Conditional";
		})(SignalTieApplicability || (exports.SignalTieApplicability = SignalTieApplicability = {}));
		exports.SignalTieApplicabilitySchema = zod_1.z.nativeEnum(SignalTieApplicability);
		var ComponentValueType;
		(function (ComponentValueType) {
		    ComponentValueType["Absolute"] = "absolute";
		    ComponentValueType["Minimum"] = "minimum";
		    ComponentValueType["Maximum"] = "maximum";
		    ComponentValueType["Range"] = "range";
		})(ComponentValueType || (exports.ComponentValueType = ComponentValueType = {}));
		exports.ComponentValueTypeSchema = zod_1.z.nativeEnum(ComponentValueType);
		exports.ExtractedSignalTieSchema = zod_1.z
		    .object({
		    toSignalName: zod_1.z.string(),
		    connectionType: zod_1.z.nativeEnum(SignalTies_1.SignalTieConnectionType),
		    componentType: zod_1.z.nativeEnum(SignalTies_1.SignalTieComponentType).nullable(),
		    componentValue: zod_1.z.string().nullable(),
		    componentValueVariance: zod_1.z.string().nullable(),
		    evidence: zod_1.z.string(),
		})
		    .strict();
		exports.SignalTiesDenormalizedSchema = zod_1.z
		    .object({
		    signalName: zod_1.z.string(),
		    ties: zod_1.z.array(exports.ExtractedSignalTieSchema),
		})
		    .strict();
		function getDenormalizedSignalTiesFromSignals(signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
		    return signals.map((signal) => {
		        const ties = [];
		        if (signal.ties) {
		            for (const externalComponent of signal.ties) {
		                const flatTie = (0, SignalTies_1.convertExternalComponentToFlatSignalTie)(externalComponent, signals, logger);
		                if (flatTie) {
		                    const extractedTie = flatTieToExtractedTie(flatTie, signals, logger);
		                    ties.push(extractedTie);
		                }
		            }
		        }
		        return {
		            signalName: signal.name,
		            ties,
		        };
		    });
		}
		function applyDenormalizedSignalTiesToSignals(signals, signalTies, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
		    const tiesMap = new Map();
		    const availableSignalNames = signals.map((s) => s.name);
		    for (const signalTie of signalTies) {
		        const matchingSignal = signals.find((s) => s.name === signalTie.signalName);
		        if (!matchingSignal) {
		            logger.warn('ApplyDenormalizedSignalTiesToSignals_SignalNotFound', {
		                signalName: signalTie.signalName,
		                availableSignalNames,
		            });
		            continue;
		        }
		        if (matchingSignal.ties && matchingSignal.ties.length > 0 && signalTie.ties.length > 0) {
		            logger.warn('ApplyDenormalizedSignalTiesToSignals_OverwritingExistingTies', {
		                signalName: matchingSignal.name,
		                existingTiesCount: matchingSignal.ties.length,
		                newTiesCount: signalTie.ties.length,
		            });
		        }
		        tiesMap.set(signalTie.signalName, signalTie.ties);
		    }
		    return signals.map((signal) => {
		        const extractedTies = tiesMap.get(signal.name);
		        if (extractedTies === undefined) {
		            return signal;
		        }
		        const externalComponents = [];
		        for (const extractedTie of extractedTies) {
		            const flatTie = extractedTieToFlatTie(extractedTie, signals, logger);
		            if (!flatTie) {
		                continue;
		            }
		            try {
		                const externalComponent = (0, SignalTies_1.convertFlatSignalTieToExternalComponent)(flatTie, signals, logger);
		                externalComponents.push(externalComponent);
		            }
		            catch (error) {
		                logger.warn('ApplyDenormalizedSignalTiesToSignals_ConversionFailed', {
		                    signalName: signal.name,
		                    extractedTie,
		                    error: error instanceof Error ? error.message : String(error),
		                });
		            }
		        }
		        return {
		            ...signal,
		            ties: externalComponents.length > 0 ? externalComponents : undefined,
		        };
		    });
		}
		function flatTieToExtractedTie(flatTie, signals, logger) {
		    const toSignalName = sigma_engine_shared_1.DesignModels.findSignalNameBySignalId(flatTie.toSignalId, signals, logger) ?? flatTie.toSignalId;
		    if (flatTie.connectionType === SignalTies_1.SignalTieConnectionType.Direct) {
		        return {
		            toSignalName,
		            connectionType: flatTie.connectionType,
		            componentType: null,
		            componentValue: null,
		            componentValueVariance: null,
		            evidence: '',
		        };
		    }
		    if (!flatTie.componentType) {
		        logger.warn('FlatTieToExtractedTie_MissingComponentType', { flatTie });
		        return {
		            toSignalName,
		            connectionType: flatTie.connectionType,
		            componentType: null,
		            componentValue: null,
		            componentValueVariance: null,
		            evidence: '',
		        };
		    }
		    if (flatTie.componentValue === null) {
		        return {
		            toSignalName,
		            connectionType: flatTie.connectionType,
		            componentType: flatTie.componentType,
		            componentValue: null,
		            componentValueVariance: null,
		            evidence: '',
		        };
		    }
		    const units = SignalTies_1.TIE_COMPONENT_TYPE_TO_UNITS[flatTie.componentType];
		    if (flatTie.componentValue.type === 'absolute') {
		        const componentValue = `${sigma_engine_shared_1.Utils.formatNumericalSIValue(flatTie.componentValue.value)}${units.short}`;
		        let componentValueVariance = null;
		        if (flatTie.componentValue.variance !== null && flatTie.componentValue.varianceType !== null) {
		            if (flatTie.componentValue.varianceType === SignalTies_1.SignalTieVarianceType.Relative) {
		                componentValueVariance = `${flatTie.componentValue.variance}%`;
		            }
		            else {
		                componentValueVariance = `${sigma_engine_shared_1.Utils.formatNumericalSIValue(flatTie.componentValue.variance)}${units.short}`;
		            }
		        }
		        return {
		            toSignalName,
		            connectionType: flatTie.connectionType,
		            componentType: flatTie.componentType,
		            componentValue,
		            componentValueVariance,
		            evidence: '',
		        };
		    }
		    if (flatTie.componentValue.type === 'range') {
		        return {
		            toSignalName,
		            connectionType: flatTie.connectionType,
		            componentType: flatTie.componentType,
		            componentValue: null,
		            componentValueVariance: null,
		            evidence: '',
		        };
		    }
		    logger.warn('FlatTieToExtractedTie_UnknownComponentValueType', { flatTie });
		    return {
		        toSignalName,
		        connectionType: flatTie.connectionType,
		        componentType: flatTie.componentType,
		        componentValue: null,
		        componentValueVariance: null,
		        evidence: '',
		    };
		}
		function extractedTieToFlatTie(extractedTie, signals, logger) {
		    const toSignalId = sigma_engine_shared_1.DesignModels.findSignalIdBySignalName(extractedTie.toSignalName, signals, logger);
		    if (!toSignalId) {
		        logger.warn('ExtractedTieToFlatTie_ToSignalNotFound', {
		            toSignalName: extractedTie.toSignalName,
		            availableSignalNames: signals.map((s) => s.name),
		        });
		        return null;
		    }
		    if (extractedTie.connectionType === SignalTies_1.SignalTieConnectionType.Direct) {
		        return {
		            toSignalId,
		            connectionType: extractedTie.connectionType,
		            componentType: null,
		            componentValue: null,
		        };
		    }
		    if (!extractedTie.componentType || !extractedTie.componentValue) {
		        logger.warn('ExtractedTieToFlatTie_MissingComponentData', { extractedTie });
		        return null;
		    }
		    const characteristic = SignalTies_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[extractedTie.componentType];
		    const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
		    const componentValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(extractedTie.componentValue, siDefinition);
		    if (componentValue === undefined) {
		        logger.warn('ExtractedTieToFlatTie_InvalidComponentValue', {
		            componentValue: extractedTie.componentValue,
		        });
		        return null;
		    }
		    let variance = null;
		    let varianceType = null;
		    if (extractedTie.componentValueVariance) {
		        const varianceProps = sigma_engine_shared_1.VarianceChecker.valueVarianceToProperties({ variance: extractedTie.componentValueVariance }, siDefinition);
		        if (varianceProps === undefined) {
		            logger.warn('ExtractedTieToFlatTie_InvalidVariance', {
		                componentValueVariance: extractedTie.componentValueVariance,
		            });
		            return null;
		        }
		        const isRelativeVariance = varianceProps.units.short === sigma_engine_shared_1.CommonParameterUnits.percentage.short;
		        varianceType = isRelativeVariance ? SignalTies_1.SignalTieVarianceType.Relative : SignalTies_1.SignalTieVarianceType.Absolute;
		        variance = varianceProps.value;
		    }
		    return {
		        toSignalId,
		        connectionType: extractedTie.connectionType,
		        componentType: extractedTie.componentType,
		        componentValue: {
		            type: 'absolute',
		            value: componentValue,
		            variance,
		            varianceType,
		        },
		    };
		}
		const COMPONENT_TYPE_TO_UNIT = {
		    [SignalTies_1.SignalTieComponentType.Capacitor]: sigma_engine_shared_1.CommonParameterUnits.capacitance,
		    [SignalTies_1.SignalTieComponentType.Resistor]: sigma_engine_shared_1.CommonParameterUnits.resistance,
		    [SignalTies_1.SignalTieComponentType.Inductor]: sigma_engine_shared_1.CommonParameterUnits.inductance,
		};
		function normalizeComponentValue(value, componentType) {
		    if (!value || !componentType) {
		        return value;
		    }
		    const unitDecl = COMPONENT_TYPE_TO_UNIT[componentType];
		    if (!unitDecl) {
		        return value;
		    }
		    try {
		        const numericValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(value, unitDecl);
		        if (numericValue === undefined) {
		            return value;
		        }
		        return sigma_engine_shared_1.Utils.formatNumericalSIValue(numericValue) + unitDecl.short;
		    }
		    catch {
		        return value;
		    }
		}
		function convertZeroComponentValueToNull(value, componentType) {
		    if (!value || !componentType) {
		        return value;
		    }
		    const unitDecl = COMPONENT_TYPE_TO_UNIT[componentType];
		    if (!unitDecl) {
		        return value;
		    }
		    try {
		        const numericValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(value, unitDecl);
		        if (numericValue === 0) {
		            return null;
		        }
		        return value;
		    }
		    catch {
		        return value;
		    }
		}
		function convertZeroValuesToNullInSignalTies(signalTies) {
		    return signalTies.map((signal) => ({
		        ...signal,
		        ties: signal.ties.map((tie) => ({
		            ...tie,
		            componentValue: convertZeroComponentValueToNull(tie.componentValue, tie.componentType),
		        })),
		    }));
		}
		function createDeduplicationKey(signalName, tie) {
		    const normalizedValue = normalizeComponentValue(tie.componentValue, tie.componentType);
		    const valueKey = normalizedValue?.toLowerCase().replace(/\s+/g, '') ?? 'null';
		    return [
		        signalName.toLowerCase(),
		        tie.toSignalName.toLowerCase(),
		        tie.connectionType,
		        tie.componentType ?? 'null',
		        valueKey,
		        tie.componentValueVariance ?? 'null',
		    ].join('|');
		}
		function normalizeAndDeduplicateSignalTies(signalTies, directionRule) {
		    return deduplicateSignalTies(directionRule.canonicalize(signalTies));
		}
		function deduplicateSignalTies(signalTies) {
		    const tiesByKey = new Map();
		    for (const signalTie of signalTies) {
		        for (const tie of signalTie.ties) {
		            const key = createDeduplicationKey(signalTie.signalName, tie);
		            const existing = tiesByKey.get(key);
		            if (existing) {
		                existing.tie.evidence = [existing.tie.evidence, tie.evidence]
		                    .filter((evidence) => evidence.trim().length > 0)
		                    .join(' ; ');
		            }
		            else {
		                tiesByKey.set(key, { signalName: signalTie.signalName, tie: { ...tie } });
		            }
		        }
		    }
		    const tiesBySignal = new Map();
		    for (const { signalName, tie } of tiesByKey.values()) {
		        const ties = tiesBySignal.get(signalName) ?? [];
		        ties.push(tie);
		        tiesBySignal.set(signalName, ties);
		    }
		    return [...tiesBySignal.entries()].map(([signalName, ties]) => ({ signalName, ties }));
		}
		
	} (signalTiesDenormalized));
	return signalTiesDenormalized;
}

var signalTiesDenormalized_validation = {};

var hasRequiredSignalTiesDenormalized_validation;

function requireSignalTiesDenormalized_validation () {
	if (hasRequiredSignalTiesDenormalized_validation) return signalTiesDenormalized_validation;
	hasRequiredSignalTiesDenormalized_validation = 1;
	Object.defineProperty(signalTiesDenormalized_validation, "__esModule", { value: true });
	signalTiesDenormalized_validation.SignalTiesValidationCheckType = void 0;
	signalTiesDenormalized_validation.validateSignalTies = validateSignalTies;
	const sigma_engine_shared_1 = requireDist$1();
	var SignalTiesValidationCheckType;
	(function (SignalTiesValidationCheckType) {
	    SignalTiesValidationCheckType["SelfReferentialTiesShouldNotExist"] = "SelfReferentialTiesShouldNotExist";
	    SignalTiesValidationCheckType["ComponentValueShouldParse"] = "ComponentValueShouldParse";
	})(SignalTiesValidationCheckType || (signalTiesDenormalized_validation.SignalTiesValidationCheckType = SignalTiesValidationCheckType = {}));
	function validateSignalTies(signalTies, logger) {
	    const flattenedTies = [];
	    for (const signalTie of signalTies) {
	        for (const tie of signalTie.ties) {
	            flattenedTies.push({
	                signalName: signalTie.signalName,
	                tie,
	            });
	        }
	    }
	    const { validatedEntries: validFlattenedTies, validationResults } = sigma_engine_shared_1.TableValidationTools.applyValidationCheck(flattenedTies, (entries) => {
	        const failures = [];
	        entries.forEach((entry, i) => {
	            if (entry.signalName === entry.tie.toSignalName) {
	                failures.push({
	                    index: i,
	                    message: `Self-referential tie: ${entry.signalName} → ${entry.tie.toSignalName}`,
	                    details: `Connection type: ${entry.tie.connectionType}`,
	                });
	            }
	        });
	        return failures;
	    }, SignalTiesValidationCheckType.SelfReferentialTiesShouldNotExist, {
	        dropFailingEntries: true,
	        logger,
	    });
	    const resultMap = new Map();
	    for (const flattenedTie of validFlattenedTies) {
	        const existing = resultMap.get(flattenedTie.signalName);
	        if (existing) {
	            existing.push(flattenedTie.tie);
	        }
	        else {
	            resultMap.set(flattenedTie.signalName, [flattenedTie.tie]);
	        }
	    }
	    const validatedSignalTies = [];
	    for (const [signalName, ties] of resultMap.entries()) {
	        validatedSignalTies.push({ signalName, ties });
	    }
	    return {
	        validatedSignalTies,
	        validationResults,
	    };
	}
	
	return signalTiesDenormalized_validation;
}

var signalFloatBehaviourDenormalized = {};

var hasRequiredSignalFloatBehaviourDenormalized;

function requireSignalFloatBehaviourDenormalized () {
	if (hasRequiredSignalFloatBehaviourDenormalized) return signalFloatBehaviourDenormalized;
	hasRequiredSignalFloatBehaviourDenormalized = 1;
	Object.defineProperty(signalFloatBehaviourDenormalized, "__esModule", { value: true });
	signalFloatBehaviourDenormalized.SignalFloatingBehaviourDenormalizedSchema = void 0;
	signalFloatBehaviourDenormalized.getSignalFloatingBehavioursInDenormalizedFormat = getSignalFloatingBehavioursInDenormalizedFormat;
	signalFloatBehaviourDenormalized.applyFloatingBehavioursToSignals = applyFloatingBehavioursToSignals;
	const zod_1 = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	signalFloatBehaviourDenormalized.SignalFloatingBehaviourDenormalizedSchema = zod_1.z
	    .object({
	    signalName: zod_1.z.string(),
	    floatingBehaviour: zod_1.z.nativeEnum(sigma_engine_shared_1.SignalFloatingBehavior).nullable(),
	    evidence: zod_1.z.string().nullable(),
	})
	    .strict();
	function getSignalFloatingBehavioursInDenormalizedFormat(signals) {
	    return signals.map((signal) => ({
	        signalName: signal.name,
	        floatingBehaviour: signal.floatingBehavior ?? null,
	        evidence: '',
	    }));
	}
	function applyFloatingBehavioursToSignals(signals, floatingBehaviours, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
	    const floatingBehaviourMap = new Map();
	    const availableSignalNames = signals.map((s) => s.name);
	    for (const behaviour of floatingBehaviours) {
	        const matchingSignal = signals.find((s) => s.name === behaviour.signalName);
	        if (!matchingSignal) {
	            logger.warn('ApplyFloatingBehavioursToSignals_SignalNotFound', {
	                signalName: behaviour.signalName,
	                availableSignalNames,
	            });
	            continue;
	        }
	        if (matchingSignal.floatingBehavior !== undefined &&
	            matchingSignal.floatingBehavior !== behaviour.floatingBehaviour) {
	            logger.warn('ApplyFloatingBehavioursToSignals_OverwritingExistingValue', {
	                signalName: matchingSignal.name,
	                existingValue: matchingSignal.floatingBehavior,
	                newValue: behaviour.floatingBehaviour,
	            });
	        }
	        floatingBehaviourMap.set(behaviour.signalName, behaviour.floatingBehaviour);
	    }
	    return signals.map((signal) => {
	        const behaviour = floatingBehaviourMap.get(signal.name);
	        if (behaviour === undefined) {
	            return signal;
	        }
	        return {
	            ...signal,
	            floatingBehavior: behaviour ?? undefined,
	        };
	    });
	}
	
	return signalFloatBehaviourDenormalized;
}

var signalIdOrphanPruning = {};

var hasRequiredSignalIdOrphanPruning;

function requireSignalIdOrphanPruning () {
	if (hasRequiredSignalIdOrphanPruning) return signalIdOrphanPruning;
	hasRequiredSignalIdOrphanPruning = 1;
	Object.defineProperty(signalIdOrphanPruning, "__esModule", { value: true });
	signalIdOrphanPruning.pruneBoundaryOrphans = pruneBoundaryOrphans;
	signalIdOrphanPruning.pruneSpiceModelOrphans = pruneSpiceModelOrphans;
	signalIdOrphanPruning.prunePinOrphans = prunePinOrphans;
	signalIdOrphanPruning.pruneTieOrphans = pruneTieOrphans;
	const sigma_engine_shared_1 = requireDist$1();
	const boundary_conditions_denormalized_1 = requireBoundaryConditionsDenormalized();
	function identityMap(signalIds) {
	    const map = new Map();
	    for (const id of signalIds) {
	        map.set(id, id);
	    }
	    return map;
	}
	function pruneBoundaryOrphans(boundaries, signalIds, onOrphan) {
	    if (boundaries.length === 0) {
	        return [];
	    }
	    const identity = identityMap(signalIds);
	    return boundaries.filter((boundary, index) => {
	        let orphaned = false;
	        (0, boundary_conditions_denormalized_1.remapSignalIdsInBoundaries)([boundary], identity, {
	            onOrphan: (orphanId, fieldPath) => {
	                orphaned = true;
	                onOrphan?.(orphanId, fieldPath.replace(/^boundaries\[0\]/, `boundaries[${index}]`));
	            },
	        });
	        return !orphaned;
	    });
	}
	function pruneSpiceModelOrphans(submodels, signalIds, onOrphan) {
	    return submodels.filter((submodel) => {
	        if (!sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel(submodel)) {
	            return true;
	        }
	        const orphan = submodel.modelInfo.signalConnectionMap.find((item) => !signalIds.has(String(item.componentSignalId)));
	        if (orphan !== undefined) {
	            onOrphan?.(String(orphan.componentSignalId), `${submodel.subModelId}.signalConnectionMap`);
	            return false;
	        }
	        return true;
	    });
	}
	function prunePinOrphans(pins, signalIds, onOrphan) {
	    return pins.filter((pin) => {
	        if (pin.signalRef === null || pin.signalRef === undefined) {
	            return true;
	        }
	        if (signalIds.has(pin.signalRef)) {
	            return true;
	        }
	        onOrphan?.(pin.signalRef, `pins.${pin.name ?? pin.signalRef}`);
	        return false;
	    });
	}
	function pruneTieOrphans(signals, signalIds, onOrphan) {
	    return signals.map((signal) => {
	        if (signal.ties === undefined || signal.ties.length === 0) {
	            return signal;
	        }
	        const ties = signal.ties.filter((tie, index) => {
	            const targets = [
	                tie.rootComponentSignalId,
	                ...(tie.withConnections ?? []).flatMap((connection) => (connection.components ?? []).map((component) => component.rootComponentSignalId)),
	            ].filter((target) => target !== undefined);
	            const orphan = targets.find((target) => !signalIds.has(target));
	            if (orphan === undefined) {
	                return true;
	            }
	            onOrphan?.(orphan, `signals.${signal.id}.ties[${index}]`);
	            return false;
	        });
	        if (ties.length === signal.ties.length) {
	            return signal;
	        }
	        return { ...signal, ties: ties.length > 0 ? ties : undefined };
	    });
	}
	
	return signalIdOrphanPruning;
}

var boundaryConditionsNormalized = {};

var hasRequiredBoundaryConditionsNormalized;

function requireBoundaryConditionsNormalized () {
	if (hasRequiredBoundaryConditionsNormalized) return boundaryConditionsNormalized;
	hasRequiredBoundaryConditionsNormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OCS_SI_COLUMN_FORMATS = exports.OCS_DEDUPLICATION_KEYS = exports.AbsoluteMaximumRatingsNormalizedSchema = exports.BoundaryConditionNormalizedForAmrsSchema = exports.OperatingCharacteristicsNormalizedSchema = exports.BoundaryConditionNormalizedSchema = exports.NormalizedBoundaryConditionTypeWithoutNominal = exports.NormalizedBoundaryConditionType = void 0;
		exports.convertComponentBoundariesToNormalizedFormat = convertComponentBoundariesToNormalizedFormat;
		exports.convertExtractedBoundariesToNormalizedFormat = convertExtractedBoundariesToNormalizedFormat;
		exports.convertComponentBoundariesToNormalizedAmrFormat = convertComponentBoundariesToNormalizedAmrFormat;
		exports.convertExtractedAmrBoundariesToNormalizedFormat = convertExtractedAmrBoundariesToNormalizedFormat;
		exports.ocsPrescoreTransform = ocsPrescoreTransform;
		const zod_1 = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		var NormalizedBoundaryConditionType;
		(function (NormalizedBoundaryConditionType) {
		    NormalizedBoundaryConditionType["minimum"] = "minimum";
		    NormalizedBoundaryConditionType["nominal"] = "nominal";
		    NormalizedBoundaryConditionType["maximum"] = "maximum";
		})(NormalizedBoundaryConditionType || (exports.NormalizedBoundaryConditionType = NormalizedBoundaryConditionType = {}));
		var NormalizedBoundaryConditionTypeWithoutNominal;
		(function (NormalizedBoundaryConditionTypeWithoutNominal) {
		    NormalizedBoundaryConditionTypeWithoutNominal["minimum"] = "minimum";
		    NormalizedBoundaryConditionTypeWithoutNominal["maximum"] = "maximum";
		})(NormalizedBoundaryConditionTypeWithoutNominal || (exports.NormalizedBoundaryConditionTypeWithoutNominal = NormalizedBoundaryConditionTypeWithoutNominal = {}));
		exports.BoundaryConditionNormalizedSchema = zod_1.z.object({
		    signalName: zod_1.z.string(),
		    characteristic: sigma_engine_shared_1.BoundaryCharacteristicSchema,
		    normalizedBoundaryConditionType: zod_1.z.nativeEnum(NormalizedBoundaryConditionType),
		    relativeToSignalName: zod_1.z.string(),
		    value: zod_1.z.string(),
		});
		exports.OperatingCharacteristicsNormalizedSchema = zod_1.z.object({
		    operatingCharacteristics: zod_1.z.array(exports.BoundaryConditionNormalizedSchema),
		});
		exports.BoundaryConditionNormalizedForAmrsSchema = exports.BoundaryConditionNormalizedSchema.extend({
		    normalizedBoundaryConditionType: zod_1.z.nativeEnum(NormalizedBoundaryConditionTypeWithoutNominal),
		});
		exports.AbsoluteMaximumRatingsNormalizedSchema = zod_1.z.object({
		    amrs: zod_1.z.array(exports.BoundaryConditionNormalizedForAmrsSchema),
		});
		function _convertComponentBoundariesToNormalizedFormat(boundaries, signals, includeNominal) {
		    const result = [];
		    const nominalBoundaries = [];
		    for (const boundaryWrapper of boundaries) {
		        const boundary = boundaryWrapper.boundary;
		        if (boundary.type !== 'numerical') {
		            continue;
		        }
		        const numerical = boundary.numerical;
		        const targetSignalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, boundary.target);
		        const relativeSignalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, boundary.relative_to);
		        const relativeSignal = relativeSignalIds.length > 0 ? signals.find((s) => s.id === relativeSignalIds[0]) : undefined;
		        const relativeSignalName = relativeSignal ? relativeSignal.name : 'RELATIVE-SIGNAL-MISSING';
		        for (const targetSignalId of targetSignalIds) {
		            const targetSignal = signals.find((s) => s.id === targetSignalId);
		            if (!targetSignal) {
		                continue;
		            }
		            if (numerical.type === 'range') {
		                if (numerical.minVal !== undefined && numerical.minVal !== null) {
		                    result.push({
		                        signalName: targetSignal.name,
		                        characteristic: numerical.characteristic,
		                        normalizedBoundaryConditionType: NormalizedBoundaryConditionType.minimum,
		                        relativeToSignalName: relativeSignalName,
		                        value: String(numerical.minVal),
		                    });
		                }
		                if (numerical.maxVal !== undefined && numerical.maxVal !== null) {
		                    result.push({
		                        signalName: targetSignal.name,
		                        characteristic: numerical.characteristic,
		                        normalizedBoundaryConditionType: NormalizedBoundaryConditionType.maximum,
		                        relativeToSignalName: relativeSignalName,
		                        value: String(numerical.maxVal),
		                    });
		                }
		                if (numerical.nominalValue !== undefined && numerical.nominalValue !== null) {
		                    const nominalBoundary = {
		                        signalName: targetSignal.name,
		                        characteristic: numerical.characteristic,
		                        normalizedBoundaryConditionType: NormalizedBoundaryConditionType.nominal,
		                        relativeToSignalName: relativeSignalName,
		                        value: String(numerical.nominalValue),
		                    };
		                    if (includeNominal) {
		                        result.push(nominalBoundary);
		                    }
		                    else {
		                        nominalBoundaries.push(nominalBoundary);
		                    }
		                }
		            }
		            else if (numerical.type === 'minimum') {
		                result.push({
		                    signalName: targetSignal.name,
		                    characteristic: numerical.characteristic,
		                    normalizedBoundaryConditionType: NormalizedBoundaryConditionType.minimum,
		                    relativeToSignalName: relativeSignalName,
		                    value: String(numerical.value),
		                });
		                if (numerical.nominalValue !== undefined && numerical.nominalValue !== null) {
		                    const nominalBoundary = {
		                        signalName: targetSignal.name,
		                        characteristic: numerical.characteristic,
		                        normalizedBoundaryConditionType: NormalizedBoundaryConditionType.nominal,
		                        relativeToSignalName: relativeSignalName,
		                        value: String(numerical.nominalValue),
		                    };
		                    if (includeNominal) {
		                        result.push(nominalBoundary);
		                    }
		                    else {
		                        nominalBoundaries.push(nominalBoundary);
		                    }
		                }
		            }
		            else if (numerical.type === 'maximum') {
		                result.push({
		                    signalName: targetSignal.name,
		                    characteristic: numerical.characteristic,
		                    normalizedBoundaryConditionType: NormalizedBoundaryConditionType.maximum,
		                    relativeToSignalName: relativeSignalName,
		                    value: String(numerical.value),
		                });
		                if (numerical.nominalValue !== undefined && numerical.nominalValue !== null) {
		                    const nominalBoundary = {
		                        signalName: targetSignal.name,
		                        characteristic: numerical.characteristic,
		                        normalizedBoundaryConditionType: NormalizedBoundaryConditionType.nominal,
		                        relativeToSignalName: relativeSignalName,
		                        value: String(numerical.nominalValue),
		                    };
		                    if (includeNominal) {
		                        result.push(nominalBoundary);
		                    }
		                    else {
		                        nominalBoundaries.push(nominalBoundary);
		                    }
		                }
		            }
		            else if (numerical.type === 'absolute') ;
		        }
		    }
		    if (!includeNominal && nominalBoundaries.length > 0) {
		        nominalBoundaries.forEach((boundary) => {
		            console.warn(`[AMR Conversion] Nominal value detected and excluded: signal="${boundary.signalName}", ` +
		                `characteristic="${boundary.characteristic}", value="${boundary.value}", ` +
		                `relativeToSignal="${boundary.relativeToSignalName}"`);
		        });
		    }
		    return result;
		}
		function convertComponentBoundariesToNormalizedFormat(boundaries, signals) {
		    return _convertComponentBoundariesToNormalizedFormat(boundaries, signals, true);
		}
		function convertExtractedBoundariesToNormalizedFormatShared(extractedCharacteristics, includeNominal) {
		    const result = [];
		    const nominalBoundaries = [];
		    for (const characteristic of extractedCharacteristics) {
		        if (characteristic.minimum !== null && characteristic.minimumRelativeToSignalName !== null) {
		            result.push({
		                signalName: characteristic.signalName,
		                characteristic: characteristic.characteristic,
		                normalizedBoundaryConditionType: NormalizedBoundaryConditionType.minimum,
		                relativeToSignalName: characteristic.minimumRelativeToSignalName,
		                value: characteristic.minimum,
		            });
		        }
		        if (characteristic.maximum !== null && characteristic.maximumRelativeToSignalName !== null) {
		            result.push({
		                signalName: characteristic.signalName,
		                characteristic: characteristic.characteristic,
		                normalizedBoundaryConditionType: NormalizedBoundaryConditionType.maximum,
		                relativeToSignalName: characteristic.maximumRelativeToSignalName,
		                value: characteristic.maximum,
		            });
		        }
		        if (characteristic.nominal !== undefined &&
		            characteristic.nominal !== null &&
		            characteristic.nominalRelativeToSignalName !== undefined &&
		            characteristic.nominalRelativeToSignalName !== null) {
		            const nominalBoundary = {
		                signalName: characteristic.signalName,
		                characteristic: characteristic.characteristic,
		                normalizedBoundaryConditionType: NormalizedBoundaryConditionType.nominal,
		                relativeToSignalName: characteristic.nominalRelativeToSignalName,
		                value: characteristic.nominal,
		            };
		            if (includeNominal) {
		                result.push(nominalBoundary);
		            }
		            else {
		                nominalBoundaries.push(nominalBoundary);
		            }
		        }
		    }
		    if (!includeNominal && nominalBoundaries.length > 0) {
		        nominalBoundaries.forEach((boundary) => {
		            console.warn(`[AMR Conversion] Nominal value detected and excluded: signal="${boundary.signalName}", ` +
		                `characteristic="${boundary.characteristic}", value="${boundary.value}", ` +
		                `relativeToSignal="${boundary.relativeToSignalName}"`);
		        });
		    }
		    return result;
		}
		function convertExtractedBoundariesToNormalizedFormat(extractedCharacteristics) {
		    return convertExtractedBoundariesToNormalizedFormatShared(extractedCharacteristics, true);
		}
		function convertComponentBoundariesToNormalizedAmrFormat(boundaries, signals) {
		    const allBoundaries = _convertComponentBoundariesToNormalizedFormat(boundaries, signals, false);
		    return allBoundaries
		        .filter((boundary) => boundary.normalizedBoundaryConditionType !== NormalizedBoundaryConditionType.nominal)
		        .map((boundary) => ({
		        signalName: boundary.signalName,
		        characteristic: boundary.characteristic,
		        normalizedBoundaryConditionType: boundary.normalizedBoundaryConditionType,
		        relativeToSignalName: boundary.relativeToSignalName,
		        value: boundary.value,
		    }));
		}
		function convertExtractedAmrBoundariesToNormalizedFormat(extractedAmrs) {
		    const allBoundaries = convertExtractedBoundariesToNormalizedFormatShared(extractedAmrs, false);
		    return allBoundaries
		        .filter((boundary) => boundary.normalizedBoundaryConditionType !== NormalizedBoundaryConditionType.nominal)
		        .map((boundary) => ({
		        signalName: boundary.signalName,
		        characteristic: boundary.characteristic,
		        normalizedBoundaryConditionType: boundary.normalizedBoundaryConditionType,
		        relativeToSignalName: boundary.relativeToSignalName,
		        value: boundary.value,
		    }));
		}
		exports.OCS_DEDUPLICATION_KEYS = [
		    'signalName',
		    'characteristic',
		    'normalizedBoundaryConditionType',
		    'relativeToSignalName',
		    'value',
		];
		exports.OCS_SI_COLUMN_FORMATS = {
		    value: sigma_engine_shared_1.BoundaryCharacteristic.Voltage,
		};
		function ocsPrescoreTransform(rows) {
		    return rows.map((row) => {
		        const signalName = row.signalName;
		        const characteristic = row.characteristic;
		        const normalizedBoundaryConditionType = row.normalizedBoundaryConditionType;
		        const relativeToSignalName = row.relativeToSignalName;
		        let value = row.value;
		        try {
		            const parsed = sigma_engine_shared_1.Utils.parseNumericalSIValue(row.value, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[sigma_engine_shared_1.BoundaryCharacteristic.Voltage]);
		            if (parsed !== undefined) {
		                value = String(parsed);
		            }
		        }
		        catch {
		        }
		        return {
		            compositeKey: `signalName:${signalName},characteristic:${characteristic},normalizedBoundaryConditionType:${normalizedBoundaryConditionType},relativeToSignalName:${relativeToSignalName},value:${value}`,
		        };
		    });
		}
		
	} (boundaryConditionsNormalized));
	return boundaryConditionsNormalized;
}

var pinMappingDenormalized = {};

var hasRequiredPinMappingDenormalized;

function requirePinMappingDenormalized () {
	if (hasRequiredPinMappingDenormalized) return pinMappingDenormalized;
	hasRequiredPinMappingDenormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PinMappingDenormalizedSchema = exports.PinDenormalizedSchema = void 0;
		exports.convertSignalsToPinMapping = convertSignalsToPinMapping;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.PinDenormalizedSchema = zod_1.z.object({
		    pinId: zod_1.z.string().describe('The pin number or identifier'),
		    signalName: zod_1.z.string().describe('The name of the signal attached to the pin'),
		});
		exports.PinMappingDenormalizedSchema = zod_1.z.object({
		    pins: zod_1.z.array(exports.PinDenormalizedSchema),
		});
		function convertSignalsToPinMapping(signals) {
		    const pins = signals.flatMap((signal) => {
		        const pinRefs = signal.pinRefs?.filter((ref) => ref !== null) ?? [];
		        return pinRefs.map((pinRef) => ({
		            pinId: pinRef,
		            signalName: signal.name,
		        }));
		    });
		    return { pins };
		}
		
	} (pinMappingDenormalized));
	return pinMappingDenormalized;
}

var statisticalSignificanceRating = {};

var hasRequiredStatisticalSignificanceRating;

function requireStatisticalSignificanceRating () {
	if (hasRequiredStatisticalSignificanceRating) return statisticalSignificanceRating;
	hasRequiredStatisticalSignificanceRating = 1;
	Object.defineProperty(statisticalSignificanceRating, "__esModule", { value: true });
	statisticalSignificanceRating.StatisticalSignificanceQualitativeRating = void 0;
	var StatisticalSignificanceQualitativeRating;
	(function (StatisticalSignificanceQualitativeRating) {
	    StatisticalSignificanceQualitativeRating["indistinctFromNoise"] = "indistinctFromNoise";
	    StatisticalSignificanceQualitativeRating["suggestive"] = "suggestive";
	    StatisticalSignificanceQualitativeRating["significant"] = "significant";
	    StatisticalSignificanceQualitativeRating["verySignificant"] = "verySignificant";
	})(StatisticalSignificanceQualitativeRating || (statisticalSignificanceRating.StatisticalSignificanceQualitativeRating = StatisticalSignificanceQualitativeRating = {}));
	
	return statisticalSignificanceRating;
}

var valueWithStandardDeviation = {};

var hasRequiredValueWithStandardDeviation;

function requireValueWithStandardDeviation () {
	if (hasRequiredValueWithStandardDeviation) return valueWithStandardDeviation;
	hasRequiredValueWithStandardDeviation = 1;
	Object.defineProperty(valueWithStandardDeviation, "__esModule", { value: true });
	
	return valueWithStandardDeviation;
}

var activeLowDenormalized = {};

var hasRequiredActiveLowDenormalized;

function requireActiveLowDenormalized () {
	if (hasRequiredActiveLowDenormalized) return activeLowDenormalized;
	hasRequiredActiveLowDenormalized = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ActiveLowResultSchema = exports.ActiveLowEntrySchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.ActiveLowEntrySchema = zod_1.z.object({
		    signalName: zod_1.z.string(),
		    activeLow: zod_1.z.boolean(),
		});
		exports.ActiveLowResultSchema = zod_1.z.object({
		    activeLowEntries: zod_1.z.array(exports.ActiveLowEntrySchema),
		});
		
	} (activeLowDenormalized));
	return activeLowDenormalized;
}

var adeIrDenormalized = {};

var hasRequiredAdeIrDenormalized;

function requireAdeIrDenormalized () {
	if (hasRequiredAdeIrDenormalized) return adeIrDenormalized;
	hasRequiredAdeIrDenormalized = 1;
	Object.defineProperty(adeIrDenormalized, "__esModule", { value: true });
	adeIrDenormalized.PackageDenormalizedSchema = adeIrDenormalized.AlternateFunctionDenormalizedSchema = adeIrDenormalized.StructureDenormalizedSchema = adeIrDenormalized.DifferentialPairDenormalizedSchema = adeIrDenormalized.VariantDenormalizedSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	adeIrDenormalized.VariantDenormalizedSchema = zod_1.z
	    .object({
	    variantKey: zod_1.z.string().min(1),
	    device: zod_1.z.string().min(1),
	    orderableMpns: zod_1.z.array(zod_1.z.string().min(1)),
	    packageFamilyName: zod_1.z.string().min(1),
	    pinCount: zod_1.z.number().int().positive(),
	    sharedCore: zod_1.z.boolean(),
	    featureVariant: zod_1.z.string().nullable(),
	})
	    .strict();
	adeIrDenormalized.DifferentialPairDenormalizedSchema = zod_1.z
	    .object({
	    positiveSignalName: zod_1.z.string().min(1),
	    negativeSignalName: zod_1.z.string().min(1),
	    pairName: zod_1.z.string().nullable(),
	})
	    .strict();
	adeIrDenormalized.StructureDenormalizedSchema = zod_1.z
	    .object({
	    variantKey: zod_1.z.string().min(1),
	    numPins: zod_1.z.number().int().positive(),
	    numPads: zod_1.z.number().int().positive().nullable(),
	    exposedPadNote: zod_1.z.string().nullable(),
	})
	    .strict();
	adeIrDenormalized.AlternateFunctionDenormalizedSchema = zod_1.z
	    .object({
	    pinName: zod_1.z.string().min(1),
	    afIndex: zod_1.z.number().int().min(0).nullable(),
	    functionName: zod_1.z.string().min(1),
	})
	    .strict();
	adeIrDenormalized.PackageDenormalizedSchema = zod_1.z
	    .object({
	    variantKey: zod_1.z.string().min(1),
	    manufacturerPartNumberPrimary: zod_1.z.string().min(1),
	    manufacturerName: zod_1.z.string().min(1),
	    sigmaModelType: zod_1.z.string().min(1),
	    groupRefs: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
	})
	    .strict();
	
	return adeIrDenormalized;
}

var inferredPackageProperties = {};

var hasRequiredInferredPackageProperties;

function requireInferredPackageProperties () {
	if (hasRequiredInferredPackageProperties) return inferredPackageProperties;
	hasRequiredInferredPackageProperties = 1;
	Object.defineProperty(inferredPackageProperties, "__esModule", { value: true });
	
	return inferredPackageProperties;
}

var moduleMetadataClassification = {};

var catalogueItem = {};

var hasRequiredCatalogueItem;

function requireCatalogueItem () {
	if (hasRequiredCatalogueItem) return catalogueItem;
	hasRequiredCatalogueItem = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.CATALOGUE_PRIORITY_ORDER_QUOTA_CONSERVATIVE = exports.CATALOGUE_PRIORITY_ORDER = exports.CatalogueItemMetadataSchema = exports.CatalogueItemSchema = exports.CatalogueMatchKindSchema = exports.CatalogueMatchKind = exports.CatalogueNameSchema = exports.CatalogueName = void 0;
		exports.getPrimaryCatalogueItem = getPrimaryCatalogueItem;
		exports.pickPrimaryCatalogueName = pickPrimaryCatalogueName;
		const zod_1 = /*@__PURE__*/ requireZod();
		var CatalogueName;
		(function (CatalogueName) {
		    CatalogueName["Digikey"] = "digikey";
		    CatalogueName["Octopart"] = "octopart";
		    CatalogueName["UltraLibrarian"] = "ultralibrarian";
		    CatalogueName["ParentDeployment"] = "parent_deployment";
		})(CatalogueName || (exports.CatalogueName = CatalogueName = {}));
		exports.CatalogueNameSchema = zod_1.z.nativeEnum(CatalogueName);
		var CatalogueMatchKind;
		(function (CatalogueMatchKind) {
		    CatalogueMatchKind["Exact"] = "exact";
		    CatalogueMatchKind["BasePartSuffix"] = "base_part_suffix";
		})(CatalogueMatchKind || (exports.CatalogueMatchKind = CatalogueMatchKind = {}));
		exports.CatalogueMatchKindSchema = zod_1.z.nativeEnum(CatalogueMatchKind);
		exports.CatalogueItemSchema = zod_1.z.object({
		    catalogueName: exports.CatalogueNameSchema,
		    mpn: zod_1.z.string(),
		    catalogueManufacturerName: zod_1.z.string().optional(),
		    cataloguePackageFamilyName: zod_1.z.string().optional(),
		    datasheetUrl: zod_1.z.string().optional(),
		    catalogueProductUrl: zod_1.z.string().optional(),
		    productImageUrl: zod_1.z.string().optional(),
		    description: zod_1.z.string(),
		    catalogueIds: zod_1.z.array(zod_1.z.string()),
		    parameters: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    rawMetadata: zod_1.z.unknown().optional(),
		    matchKind: exports.CatalogueMatchKindSchema.optional(),
		});
		exports.CatalogueItemMetadataSchema = zod_1.z.object({
		    catalogueIds: zod_1.z.array(zod_1.z.string()),
		    parameters: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    rawMetadata: zod_1.z.unknown().optional(),
		});
		exports.CATALOGUE_PRIORITY_ORDER = [
		    CatalogueName.Digikey,
		    CatalogueName.Octopart,
		    CatalogueName.UltraLibrarian,
		];
		exports.CATALOGUE_PRIORITY_ORDER_QUOTA_CONSERVATIVE = [
		    CatalogueName.Octopart,
		    CatalogueName.Digikey,
		    CatalogueName.UltraLibrarian,
		];
		function getPrimaryCatalogueItem(catalogueItems) {
		    if (!catalogueItems || catalogueItems.length === 0) {
		        return null;
		    }
		    const distributorItems = catalogueItems.filter((item) => item.catalogueName !== CatalogueName.ParentDeployment);
		    const pool = distributorItems.length > 0 ? distributorItems : catalogueItems;
		    const exactItems = pool.filter((item) => item.matchKind !== CatalogueMatchKind.BasePartSuffix);
		    const candidates = exactItems.length > 0 ? exactItems : pool;
		    for (const catalogueName of exports.CATALOGUE_PRIORITY_ORDER) {
		        const item = candidates.find((item) => item.catalogueName === catalogueName);
		        if (item) {
		            return item;
		        }
		    }
		    return candidates[0];
		}
		function pickPrimaryCatalogueName(catalogues) {
		    if (!catalogues || catalogues.length === 0) {
		        return null;
		    }
		    for (const name of exports.CATALOGUE_PRIORITY_ORDER) {
		        if (catalogues.includes(name)) {
		            return name;
		        }
		    }
		    return catalogues[0];
		}
		
	} (catalogueItem));
	return catalogueItem;
}

var hasRequiredModuleMetadataClassification;

function requireModuleMetadataClassification () {
	if (hasRequiredModuleMetadataClassification) return moduleMetadataClassification;
	hasRequiredModuleMetadataClassification = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.MetadataFieldClassificationSchema = exports.MetadataSupplierSourcedCandidateSchema = exports.MetadataSourcedCandidateSchema = exports.MetadataClassificationModeSchema = exports.MetadataClassificationMode = exports.MetadataCandidateSourceMethodSchema = exports.MetadataCandidateSourceMethod = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const catalogue_item_1 = requireCatalogueItem();
		var MetadataCandidateSourceMethod;
		(function (MetadataCandidateSourceMethod) {
		    MetadataCandidateSourceMethod["AiClassification"] = "ai_classification";
		    MetadataCandidateSourceMethod["Heuristic"] = "heuristic";
		})(MetadataCandidateSourceMethod || (exports.MetadataCandidateSourceMethod = MetadataCandidateSourceMethod = {}));
		exports.MetadataCandidateSourceMethodSchema = zod_1.z.nativeEnum(MetadataCandidateSourceMethod);
		var MetadataClassificationMode;
		(function (MetadataClassificationMode) {
		    MetadataClassificationMode["AiOnly"] = "ai_only";
		    MetadataClassificationMode["AiWithHeuristicFallback"] = "ai_with_heuristic_fallback";
		    MetadataClassificationMode["HeuristicOnly"] = "heuristic_only";
		})(MetadataClassificationMode || (exports.MetadataClassificationMode = MetadataClassificationMode = {}));
		exports.MetadataClassificationModeSchema = zod_1.z.nativeEnum(MetadataClassificationMode);
		exports.MetadataSourcedCandidateSchema = zod_1.z.object({
		    value: zod_1.z.string(),
		    sourceFieldName: zod_1.z.string(),
		    sourceMethod: exports.MetadataCandidateSourceMethodSchema,
		});
		exports.MetadataSupplierSourcedCandidateSchema = zod_1.z.object({
		    value: zod_1.z.string(),
		    sourceFieldName: zod_1.z.string(),
		    sourceMethod: exports.MetadataCandidateSourceMethodSchema,
		    supplier: catalogue_item_1.CatalogueNameSchema.nullable(),
		});
		exports.MetadataFieldClassificationSchema = zod_1.z.object({
		    manufacturerPartNumberSourceField: zod_1.z
		        .string()
		        .nullable()
		        .describe('Field name containing the manufacturer part number, or null if not present'),
		    manufacturerNameSourceField: zod_1.z
		        .string()
		        .nullable()
		        .describe('Field name containing the manufacturer name, or null if not present'),
		    digikeyPartNumberSourceField: zod_1.z
		        .string()
		        .nullable()
		        .describe('Field name containing the Digi-Key part number, or null if not present'),
		    supplierPartNumberSourceField: zod_1.z
		        .string()
		        .nullable()
		        .describe('Field name containing a non-DigiKey distributor / supplier part number ' +
		        '(Mouser, LCSC, Arrow, Newark, generic supplier PN columns, etc.), or null if not present'),
		    componentValueSourceField: zod_1.z
		        .string()
		        .nullable()
		        .describe('Field name containing a single component value in SI units (Ω, F, H, V), or null if not present'),
		});
		
	} (moduleMetadataClassification));
	return moduleMetadataClassification;
}

var disagreementReport = {};

var hasRequiredDisagreementReport;

function requireDisagreementReport () {
	if (hasRequiredDisagreementReport) return disagreementReport;
	hasRequiredDisagreementReport = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DisagreementReportSchema = exports.DisagreementNearMissSchema = exports.DisagreementNearMissColumnSchema = exports.DisagreementRowSchema = exports.DisagreementCellSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const slice_key_1 = requireSliceKey();
		const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
		const json_value_1 = requireJsonValue();
		exports.DisagreementCellSchema = z.object({
		    anchor: consensus_types_1.FieldAnchorSchema,
		    groundTruth: package_data_adjudication_types_1.ComparedValueSchema,
		    inferred: package_data_adjudication_types_1.ComparedValueSchema,
		});
		exports.DisagreementRowSchema = z.object({
		    anchor: consensus_types_1.RowAnchorSchema,
		    direction: package_data_adjudication_types_1.DiscrepancyDirectionSchema,
		    rowContentRaw: json_value_1.JsonValueSchema,
		});
		exports.DisagreementNearMissColumnSchema = z.object({
		    column: z.string(),
		    groundTruth: package_data_adjudication_types_1.ComparedValueSchema,
		    inferred: package_data_adjudication_types_1.ComparedValueSchema,
		});
		exports.DisagreementNearMissSchema = z.object({
		    groundTruthRowKey: z.string(),
		    inferredRowKey: z.string(),
		    matchedColumns: z.string().array(),
		    differingColumns: exports.DisagreementNearMissColumnSchema.array(),
		});
		exports.DisagreementReportSchema = z.object({
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int(),
		    cells: exports.DisagreementCellSchema.array(),
		    rows: exports.DisagreementRowSchema.array(),
		    missingColumns: z.string().array(),
		    duplicateRowKeys: z.string().array(),
		    nearMisses: exports.DisagreementNearMissSchema.array().optional(),
		});
		
	} (disagreementReport));
	return disagreementReport;
}

var experiments = {};

var scoredAiTask = {};

var hasRequiredScoredAiTask;

function requireScoredAiTask () {
	if (hasRequiredScoredAiTask) return scoredAiTask;
	hasRequiredScoredAiTask = 1;
	Object.defineProperty(scoredAiTask, "__esModule", { value: true });
	scoredAiTask.ScoredAiTask = void 0;
	var ScoredAiTask;
	(function (ScoredAiTask) {
	    ScoredAiTask["ExtractSignalsWithPinout"] = "ExtractSignalsWithPinout";
	    ScoredAiTask["ExtractSignalAttributes"] = "ExtractSignalAttributes";
	    ScoredAiTask["ExtractOperatingCharacteristics"] = "ExtractOperatingCharacteristics";
	    ScoredAiTask["ExtractOperatingCharacteristics_LocateElementsWithAgent"] = "ExtractOperatingCharacteristics_LocateElementsWithAgent";
	    ScoredAiTask["ExtractOperatingCharacteristics_ExtractRawConditionsFromTables"] = "ExtractOperatingCharacteristics_ExtractRawConditionsFromTables";
	    ScoredAiTask["ExtractOperatingCharacteristics_ParseExtractedConditions"] = "ExtractOperatingCharacteristics_ParseExtractedConditions";
	    ScoredAiTask["ExtractOperatingCharacteristics_MapConditionsToSignals"] = "ExtractOperatingCharacteristics_MapConditionsToSignals";
	    ScoredAiTask["ExtractAbsoluteMaximumRatings"] = "ExtractAbsoluteMaximumRatings";
	    ScoredAiTask["ExtractAbsoluteMaximumRatings_LocateData"] = "ExtractAbsoluteMaximumRatings_LocateData";
	    ScoredAiTask["ExtractAbsoluteMaximumRatings_LocateElementsOrPages"] = "ExtractAbsoluteMaximumRatings_LocateElementsOrPages";
	    ScoredAiTask["ExtractAbsoluteMaximumRatings_ExtractData"] = "ExtractAbsoluteMaximumRatings_ExtractData";
	    ScoredAiTask["ExtractAbsoluteMaximumRatings_ParseRatings"] = "ExtractAbsoluteMaximumRatings_ParseRatings";
	    ScoredAiTask["ExtractAbsoluteMaximumRatings_MapRatingsToSignals"] = "ExtractAbsoluteMaximumRatings_MapRatingsToSignals";
	    ScoredAiTask["ExtractSignalDriveConditions"] = "ExtractSignalDriveConditions";
	    ScoredAiTask["ExtractSignalDriveConditions_LocateLogicLevelElementsWithAgent"] = "ExtractSignalDriveConditions_LocateLogicLevelElementsWithAgent";
	    ScoredAiTask["ExtractSignalDriveConditions_ExtractLogicLevelConditionsFromTables"] = "ExtractSignalDriveConditions_ExtractLogicLevelConditionsFromTables";
	    ScoredAiTask["ExtractSignalDriveCharacteristics"] = "ExtractSignalDriveCharacteristics";
	    ScoredAiTask["ExtractFunctions"] = "ExtractFunctions";
	    ScoredAiTask["ExtractSignalTies"] = "ExtractSignalTies";
	    ScoredAiTask["ExtractSignalTies_ExtractFromRetrievedContext"] = "ExtractSignalTies_ExtractFromRetrievedContext";
	    ScoredAiTask["ExtractSignalTies_RetrieveDocumentChunks"] = "ExtractSignalTies_RetrieveDocumentChunks";
	    ScoredAiTask["ExtractActiveLow"] = "ExtractActiveLow";
	    ScoredAiTask["ExtractSignalFloatBehaviour"] = "ExtractSignalFloatBehaviour";
	    ScoredAiTask["ExtractSignalsWithPinout_IsolateDiagram"] = "ExtractSignalsWithPinout_IsolateDiagram";
	    ScoredAiTask["ExtractSignalsWithPinout_LocatePinDescriptions"] = "ExtractSignalsWithPinout_LocatePinDescriptions";
	    ScoredAiTask["ExtractSignalsWithPinout_ExtractSignalNames"] = "ExtractSignalsWithPinout_ExtractSignalNames";
	    ScoredAiTask["ExtractSignalsWithPinout_MapPinsToSignals"] = "ExtractSignalsWithPinout_MapPinsToSignals";
	    ScoredAiTask["GenerateImplementationRules"] = "GenerateImplementationRules";
	})(ScoredAiTask || (scoredAiTask.ScoredAiTask = ScoredAiTask = {}));
	
	return scoredAiTask;
}

var experimentScores = {};

var hasRequiredExperimentScores;

function requireExperimentScores () {
	if (hasRequiredExperimentScores) return experimentScores;
	hasRequiredExperimentScores = 1;
	Object.defineProperty(experimentScores, "__esModule", { value: true });
	experimentScores.ScoresSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	experimentScores.ScoresSchema = z.custom((value) => typeof value === 'object' && value !== null, {
	    message: 'overallScores must be an object',
	});
	
	return experimentScores;
}

var experimentMetadata = {};

var hasRequiredExperimentMetadata;

function requireExperimentMetadata () {
	if (hasRequiredExperimentMetadata) return experimentMetadata;
	hasRequiredExperimentMetadata = 1;
	Object.defineProperty(experimentMetadata, "__esModule", { value: true });
	experimentMetadata.ExperimentMetadataSchema = experimentMetadata.ExperimentAbortReason = void 0;
	const z = /*@__PURE__*/ requireZod();
	var ExperimentAbortReason;
	(function (ExperimentAbortReason) {
	    ExperimentAbortReason["MixedConfigFingerprint"] = "mixed_config_fingerprint";
	})(ExperimentAbortReason || (experimentMetadata.ExperimentAbortReason = ExperimentAbortReason = {}));
	experimentMetadata.ExperimentMetadataSchema = z.custom((value) => typeof value === 'object' && value !== null, { message: 'metadata must be an object' });
	
	return experimentMetadata;
}

var experimentTags = {};

var hasRequiredExperimentTags;

function requireExperimentTags () {
	if (hasRequiredExperimentTags) return experimentTags;
	hasRequiredExperimentTags = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ExperimentTagsSchema = exports.ExperimentTagSchema = exports.ExperimentTagName = void 0;
		const z = /*@__PURE__*/ requireZod();
		var ExperimentTagName;
		(function (ExperimentTagName) {
		    ExperimentTagName["Tracked"] = "Tracked";
		    ExperimentTagName["Deployed"] = "Deployed";
		})(ExperimentTagName || (exports.ExperimentTagName = ExperimentTagName = {}));
		const TrackedTagMetadataSchema = z.object({
		    explanation: z.string(),
		});
		const DeployedTagMetadataSchema = z.object({
		    pullRequest: z
		        .string()
		        .regex(/^[0-9]{4,5}$/, 'pullRequest must be a 4-5 digit numeric string')
		        .optional(),
		});
		const TrackedTagSchema = z.object({
		    name: z.literal(ExperimentTagName.Tracked),
		    metadata: TrackedTagMetadataSchema,
		});
		const DeployedTagSchema = z.object({
		    name: z.literal(ExperimentTagName.Deployed),
		    metadata: DeployedTagMetadataSchema,
		});
		exports.ExperimentTagSchema = z.discriminatedUnion('name', [
		    TrackedTagSchema,
		    DeployedTagSchema,
		]);
		exports.ExperimentTagsSchema = z.array(exports.ExperimentTagSchema);
		
	} (experimentTags));
	return experimentTags;
}

var experiment = {};

var hasRequiredExperiment;

function requireExperiment () {
	if (hasRequiredExperiment) return experiment;
	hasRequiredExperiment = 1;
	Object.defineProperty(experiment, "__esModule", { value: true });
	experiment.ExperimentSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
	const experiment_metadata_1 = requireExperimentMetadata();
	const experiment_scores_1 = requireExperimentScores();
	const experiment_tags_1 = requireExperimentTags();
	const scored_ai_task_1 = requireScoredAiTask();
	experiment.ExperimentSchema = z.object({
	    id: z.string(),
	    userName: z.string(),
	    datasetId: z.string(),
	    task: z.nativeEnum(scored_ai_task_1.ScoredAiTask),
	    description: z.string(),
	    overallScores: experiment_scores_1.ScoresSchema.optional(),
	    metadata: experiment_metadata_1.ExperimentMetadataSchema,
	    createdAt: z.date().optional(),
	    tags: experiment_tags_1.ExperimentTagsSchema,
	    assessmentStatus: package_data_adjudication_types_1.ExperimentAssessmentStatusSchema,
	    assessedAt: z.date().optional(),
	    dispatchedAt: z.date().optional(),
	});
	
	return experiment;
}

var experimentScoring = {};

var hasRequiredExperimentScoring;

function requireExperimentScoring () {
	if (hasRequiredExperimentScoring) return experimentScoring;
	hasRequiredExperimentScoring = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ExperimentScoringSchema = exports.ExperimentScoringKindSchema = exports.ExperimentScoringKind = void 0;
		const z = /*@__PURE__*/ requireZod();
		const experiment_scores_1 = requireExperimentScores();
		var ExperimentScoringKind;
		(function (ExperimentScoringKind) {
		    ExperimentScoringKind["Initial"] = "initial";
		    ExperimentScoringKind["Rescore"] = "rescore";
		})(ExperimentScoringKind || (exports.ExperimentScoringKind = ExperimentScoringKind = {}));
		exports.ExperimentScoringKindSchema = z.nativeEnum(ExperimentScoringKind);
		exports.ExperimentScoringSchema = z.object({
		    id: z.string(),
		    experimentId: z.string(),
		    kind: exports.ExperimentScoringKindSchema,
		    datasetId: z.string(),
		    scoredAt: z.date(),
		    scoredByUserId: z.string().nullable(),
		    overallScores: experiment_scores_1.ScoresSchema,
		    carriedOverScoreKeys: z.string().array(),
		});
		
	} (experimentScoring));
	return experimentScoring;
}

var experimentDataset = {};

var hasRequiredExperimentDataset;

function requireExperimentDataset () {
	if (hasRequiredExperimentDataset) return experimentDataset;
	hasRequiredExperimentDataset = 1;
	Object.defineProperty(experimentDataset, "__esModule", { value: true });
	experimentDataset.ChangedDatasetPackageSchema = experimentDataset.ExperimentDatasetRefSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	experimentDataset.ExperimentDatasetRefSchema = z.object({
	    id: z.string(),
	    name: z.string(),
	    version: z.number().int(),
	});
	experimentDataset.ChangedDatasetPackageSchema = z.object({
	    packageId: z.string(),
	    mpn: z.string(),
	    previousSnapshotId: z.string(),
	    snapshotId: z.string(),
	});
	
	return experimentDataset;
}

var experimentAssessment = {};

var hasRequiredExperimentAssessment;

function requireExperimentAssessment () {
	if (hasRequiredExperimentAssessment) return experimentAssessment;
	hasRequiredExperimentAssessment = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ExperimentAssessmentRecordSchema = exports.ExperimentAssessmentReportSchema = exports.ExperimentAssessmentRaisedSchema = exports.ExperimentAssessModeSchema = exports.ExperimentAssessMode = void 0;
		const z = /*@__PURE__*/ requireZod();
		const experiment_error_profile_types_1 = requireExperimentErrorProfile_types();
		const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
		var ExperimentAssessMode;
		(function (ExperimentAssessMode) {
		    ExperimentAssessMode["Dispatched"] = "dispatched";
		    ExperimentAssessMode["Refreshed"] = "refreshed";
		})(ExperimentAssessMode || (exports.ExperimentAssessMode = ExperimentAssessMode = {}));
		exports.ExperimentAssessModeSchema = z.nativeEnum(ExperimentAssessMode);
		exports.ExperimentAssessmentRaisedSchema = z.object({
		    newRequests: z.number().int(),
		    joinedRequests: z.number().int(),
		    wouldRaiseDisagreements: z.number().int(),
		});
		exports.ExperimentAssessmentReportSchema = z.object({
		    experimentId: z.string(),
		    mode: exports.ExperimentAssessModeSchema,
		    assessmentStatus: package_data_adjudication_types_1.ExperimentAssessmentStatusSchema,
		    profile: experiment_error_profile_types_1.ExperimentErrorProfileSchema,
		    raised: exports.ExperimentAssessmentRaisedSchema,
		    assessedAt: z.date(),
		});
		exports.ExperimentAssessmentRecordSchema = z.object({
		    experimentId: z.string(),
		    assessmentStatus: package_data_adjudication_types_1.ExperimentAssessmentStatusSchema,
		    errorProfile: experiment_error_profile_types_1.ExperimentErrorProfileSchema.nullable(),
		    assessedAt: z.date().nullable(),
		    dispatchedAt: z.date().nullable(),
		});
		
	} (experimentAssessment));
	return experimentAssessment;
}

var experimentRescore = {};

var hasRequiredExperimentRescore;

function requireExperimentRescore () {
	if (hasRequiredExperimentRescore) return experimentRescore;
	hasRequiredExperimentRescore = 1;
	Object.defineProperty(experimentRescore, "__esModule", { value: true });
	experimentRescore.ExperimentRescoreResultSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const experiment_error_profile_types_1 = requireExperimentErrorProfile_types();
	const experiment_dataset_1 = requireExperimentDataset();
	const experiment_scoring_1 = requireExperimentScoring();
	experimentRescore.ExperimentRescoreResultSchema = z.object({
	    scoring: experiment_scoring_1.ExperimentScoringSchema,
	    dataset: experiment_dataset_1.ExperimentDatasetRefSchema,
	    changedPackages: experiment_dataset_1.ChangedDatasetPackageSchema.array(),
	    carriedOverScoreKeys: z.string().array(),
	    profile: experiment_error_profile_types_1.ExperimentErrorProfileSchema,
	});
	
	return experimentRescore;
}

var hasRequiredExperiments;

function requireExperiments () {
	if (hasRequiredExperiments) return experiments;
	hasRequiredExperiments = 1;
	(function (exports) {
		var __createBinding = (experiments && experiments.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (experiments && experiments.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireScoredAiTask(), exports);
		__exportStar(requireExperimentScores(), exports);
		__exportStar(requireExperimentMetadata(), exports);
		__exportStar(requireExperimentTags(), exports);
		__exportStar(requireExperiment(), exports);
		__exportStar(requireExperimentScoring(), exports);
		__exportStar(requireExperimentDataset(), exports);
		__exportStar(requireExperimentAssessment(), exports);
		__exportStar(requireExperimentRescore(), exports);
		
	} (experiments));
	return experiments;
}

var formulas = {};

var metricsFormulas = {};

var hasRequiredMetricsFormulas;

function requireMetricsFormulas () {
	if (hasRequiredMetricsFormulas) return metricsFormulas;
	hasRequiredMetricsFormulas = 1;
	Object.defineProperty(metricsFormulas, "__esModule", { value: true });
	metricsFormulas.precision = precision;
	metricsFormulas.recall = recall;
	metricsFormulas.f1 = f1;
	const lodash_1 = requireLodash();
	function precision({ truePositives, falsePositives, numDigits = 3, }) {
	    const result = truePositives + falsePositives === 0 ? 0 : truePositives / (truePositives + falsePositives);
	    return (0, lodash_1.round)(result, numDigits);
	}
	function recall({ truePositives, falseNegatives, numDigits = 3, }) {
	    const result = truePositives + falseNegatives === 0 ? 0 : truePositives / (truePositives + falseNegatives);
	    return (0, lodash_1.round)(result, numDigits);
	}
	function f1({ precision, recall, numDigits = 3, }) {
	    const result = precision + recall === 0 ? 0 : (2 * (precision * recall)) / (precision + recall);
	    return (0, lodash_1.round)(result, numDigits);
	}
	
	return metricsFormulas;
}

var statisticsFormulas = {};

/**
* @license Apache-2.0
*
* Copyright (c) 2021 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var define_property;
var hasRequiredDefine_property;

function requireDefine_property () {
	if (hasRequiredDefine_property) return define_property;
	hasRequiredDefine_property = 1;

	// MAIN //

	var main = ( typeof Object.defineProperty === 'function' ) ? Object.defineProperty : null;


	// EXPORTS //

	define_property = main;
	return define_property;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2021 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var has_define_property_support;
var hasRequiredHas_define_property_support;

function requireHas_define_property_support () {
	if (hasRequiredHas_define_property_support) return has_define_property_support;
	hasRequiredHas_define_property_support = 1;

	// MODULES //

	var defineProperty = requireDefine_property();


	// MAIN //

	/**
	* Tests for `Object.defineProperty` support.
	*
	* @private
	* @returns {boolean} boolean indicating if an environment has `Object.defineProperty` support
	*
	* @example
	* var bool = hasDefinePropertySupport();
	* // returns <boolean>
	*/
	function hasDefinePropertySupport() {
		// Test basic support...
		try {
			defineProperty( {}, 'x', {} );
			return true;
		} catch ( err ) { // eslint-disable-line no-unused-vars
			return false;
		}
	}


	// EXPORTS //

	has_define_property_support = hasDefinePropertySupport;
	return has_define_property_support;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var builtin;
var hasRequiredBuiltin;

function requireBuiltin () {
	if (hasRequiredBuiltin) return builtin;
	hasRequiredBuiltin = 1;

	// MAIN //

	/**
	* Defines (or modifies) an object property.
	*
	* ## Notes
	*
	* -   Property descriptors come in two flavors: **data descriptors** and **accessor descriptors**. A data descriptor is a property that has a value, which may or may not be writable. An accessor descriptor is a property described by a getter-setter function pair. A descriptor must be one of these two flavors and cannot be both.
	*
	* @name defineProperty
	* @type {Function}
	* @param {Object} obj - object on which to define the property
	* @param {(string|symbol)} prop - property name
	* @param {Object} descriptor - property descriptor
	* @param {boolean} [descriptor.configurable=false] - boolean indicating if property descriptor can be changed and if the property can be deleted from the provided object
	* @param {boolean} [descriptor.enumerable=false] - boolean indicating if the property shows up when enumerating object properties
	* @param {boolean} [descriptor.writable=false] - boolean indicating if the value associated with the property can be changed with an assignment operator
	* @param {*} [descriptor.value] - property value
	* @param {(Function|void)} [descriptor.get=undefined] - function which serves as a getter for the property, or, if no getter, undefined. When the property is accessed, a getter function is called without arguments and with the `this` context set to the object through which the property is accessed (which may not be the object on which the property is defined due to inheritance). The return value will be used as the property value.
	* @param {(Function|void)} [descriptor.set=undefined] - function which serves as a setter for the property, or, if no setter, undefined. When assigning a property value, a setter function is called with one argument (the value being assigned to the property) and with the `this` context set to the object through which the property is assigned.
	* @throws {TypeError} first argument must be an object
	* @throws {TypeError} third argument must be an object
	* @throws {Error} property descriptor cannot have both a value and a setter and/or getter
	* @returns {Object} object with added property
	*
	* @example
	* var obj = {};
	*
	* defineProperty( obj, 'foo', {
	*     'value': 'bar'
	* });
	*
	* var str = obj.foo;
	* // returns 'bar'
	*/
	var defineProperty = Object.defineProperty;


	// EXPORTS //

	builtin = defineProperty;
	return builtin;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var is_number;
var hasRequiredIs_number;

function requireIs_number () {
	if (hasRequiredIs_number) return is_number;
	hasRequiredIs_number = 1;

	/**
	* Tests if a value is a number primitive.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a number primitive
	*
	* @example
	* var bool = isNumber( 3.14 );
	* // returns true
	*
	* @example
	* var bool = isNumber( NaN );
	* // returns true
	*
	* @example
	* var bool = isNumber( new Number( 3.14 ) );
	* // returns false
	*/
	function isNumber( value ) {
		return ( typeof value === 'number' );  // NOTE: we inline the `isNumber.isPrimitive` function from `@stdlib/assert/is-number` in order to avoid circular dependencies.
	}


	// EXPORTS //

	is_number = isNumber;
	return is_number;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var zero_pad;
var hasRequiredZero_pad;

function requireZero_pad () {
	if (hasRequiredZero_pad) return zero_pad;
	hasRequiredZero_pad = 1;

	// FUNCTIONS //

	/**
	* Tests if a string starts with a minus sign (`-`).
	*
	* @private
	* @param {string} str - input string
	* @returns {boolean} boolean indicating if a string starts with a minus sign (`-`)
	*/
	function startsWithMinus( str ) {
		return str[ 0 ] === '-';
	}

	/**
	* Returns a string of `n` zeros.
	*
	* @private
	* @param {number} n - number of zeros
	* @returns {string} string of zeros
	*/
	function zeros( n ) {
		var out = '';
		var i;
		for ( i = 0; i < n; i++ ) {
			out += '0';
		}
		return out;
	}


	// MAIN //

	/**
	* Pads a token with zeros to the specified width.
	*
	* @private
	* @param {string} str - token argument
	* @param {number} width - token width
	* @param {boolean} [right=false] - boolean indicating whether to pad to the right
	* @returns {string} padded token argument
	*/
	function zeroPad( str, width, right ) {
		var negative = false;
		var pad = width - str.length;
		if ( pad < 0 ) {
			return str;
		}
		if ( startsWithMinus( str ) ) {
			negative = true;
			str = str.substr( 1 );
		}
		str = ( right ) ?
			str + zeros( pad ) :
			zeros( pad ) + str;
		if ( negative ) {
			str = '-' + str;
		}
		return str;
	}


	// EXPORTS //

	zero_pad = zeroPad;
	return zero_pad;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var format_integer;
var hasRequiredFormat_integer;

function requireFormat_integer () {
	if (hasRequiredFormat_integer) return format_integer;
	hasRequiredFormat_integer = 1;

	// MODULES //

	var isNumber = requireIs_number();
	var zeroPad = requireZero_pad();

	// NOTE: for the following, we explicitly avoid using stdlib packages in this particular package in order to avoid circular dependencies.
	var lowercase = String.prototype.toLowerCase;
	var uppercase = String.prototype.toUpperCase;


	// MAIN //

	/**
	* Formats a token object argument as an integer.
	*
	* @private
	* @param {Object} token - token object
	* @throws {Error} must provide a valid integer
	* @returns {string} formatted token argument
	*/
	function formatInteger( token ) {
		var base;
		var out;
		var i;

		switch ( token.specifier ) {
		case 'b':
			// Case: %b (binary)
			base = 2;
			break;
		case 'o':
			// Case: %o (octal)
			base = 8;
			break;
		case 'x':
		case 'X':
			// Case: %x, %X (hexadecimal)
			base = 16;
			break;
		case 'd':
		case 'i':
		case 'u':
		default:
			// Case: %d, %i, %u (decimal)
			base = 10;
			break;
		}
		out = token.arg;
		i = parseInt( out, 10 );
		if ( !isFinite( i ) ) { // NOTE: We use the global `isFinite` function here instead of `@stdlib/math/base/assert/is-finite` in order to avoid circular dependencies.
			if ( !isNumber( out ) ) {
				throw new Error( 'invalid integer. Value: ' + out );
			}
			i = 0;
		}
		if ( i < 0 && ( token.specifier === 'u' || base !== 10 ) ) {
			i = 0xffffffff + i + 1;
		}
		if ( i < 0 ) {
			out = ( -i ).toString( base );
			if ( token.precision ) {
				out = zeroPad( out, token.precision, token.padRight );
			}
			out = '-' + out;
		} else {
			out = i.toString( base );
			if ( !i && !token.precision ) {
				out = '';
			} else if ( token.precision ) {
				out = zeroPad( out, token.precision, token.padRight );
			}
			if ( token.sign ) {
				out = token.sign + out;
			}
		}
		if ( base === 16 ) {
			if ( token.alternate ) {
				out = '0x' + out;
			}
			out = ( token.specifier === uppercase.call( token.specifier ) ) ?
				uppercase.call( out ) :
				lowercase.call( out );
		}
		if ( base === 8 ) {
			if ( token.alternate && out.charAt( 0 ) !== '0' ) {
				out = '0' + out;
			}
		}
		return out;
	}


	// EXPORTS //

	format_integer = formatInteger;
	return format_integer;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var is_string$1;
var hasRequiredIs_string$1;

function requireIs_string$1 () {
	if (hasRequiredIs_string$1) return is_string$1;
	hasRequiredIs_string$1 = 1;

	/**
	* Tests if a value is a string primitive.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a string primitive
	*
	* @example
	* var bool = isString( 'beep' );
	* // returns true
	*
	* @example
	* var bool = isString( new String( 'beep' ) );
	* // returns false
	*/
	function isString( value ) {
		return ( typeof value === 'string' ); // NOTE: we inline the `isString.isPrimitive` function from `@stdlib/assert/is-string` in order to avoid circular dependencies.
	}


	// EXPORTS //

	is_string$1 = isString;
	return is_string$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var format_double;
var hasRequiredFormat_double;

function requireFormat_double () {
	if (hasRequiredFormat_double) return format_double;
	hasRequiredFormat_double = 1;

	// MODULES //

	var isNumber = requireIs_number();

	// NOTE: for the following, we explicitly avoid using stdlib packages in this particular package in order to avoid circular dependencies.
	var abs = Math.abs; // eslint-disable-line stdlib/no-builtin-math
	var lowercase = String.prototype.toLowerCase;
	var uppercase = String.prototype.toUpperCase;
	var replace = String.prototype.replace;


	// VARIABLES //

	var RE_EXP_POS_DIGITS = /e\+(\d)$/;
	var RE_EXP_NEG_DIGITS = /e-(\d)$/;
	var RE_ONLY_DIGITS = /^(\d+)$/;
	var RE_DIGITS_BEFORE_EXP = /^(\d+)e/;
	var RE_TRAILING_PERIOD_ZERO = /\.0$/;
	var RE_PERIOD_ZERO_EXP = /\.0*e/;
	var RE_ZERO_BEFORE_EXP = /(\..*[^0])0*e/;


	// MAIN //

	/**
	* Formats a token object argument as a floating-point number.
	*
	* @private
	* @param {Object} token - token object
	* @throws {Error} must provide a valid floating-point number
	* @returns {string} formatted token argument
	*/
	function formatDouble( token ) {
		var digits;
		var out;
		var f = parseFloat( token.arg );
		if ( !isFinite( f ) ) { // NOTE: We use the global `isFinite` function here instead of `@stdlib/math/base/assert/is-finite` in order to avoid circular dependencies.
			if ( !isNumber( token.arg ) ) {
				throw new Error( 'invalid floating-point number. Value: ' + out );
			}
			// Case: NaN, Infinity, or -Infinity
			f = token.arg;
		}
		switch ( token.specifier ) {
		case 'e':
		case 'E':
			out = f.toExponential( token.precision );
			break;
		case 'f':
		case 'F':
			out = f.toFixed( token.precision );
			break;
		case 'g':
		case 'G':
			if ( abs( f ) < 0.0001 ) {
				digits = token.precision;
				if ( digits > 0 ) {
					digits -= 1;
				}
				out = f.toExponential( digits );
			} else {
				out = f.toPrecision( token.precision );
			}
			if ( !token.alternate ) {
				out = replace.call( out, RE_ZERO_BEFORE_EXP, '$1e' );
				out = replace.call( out, RE_PERIOD_ZERO_EXP, 'e' );
				out = replace.call( out, RE_TRAILING_PERIOD_ZERO, '' );
			}
			break;
		default:
			throw new Error( 'invalid double notation. Value: ' + token.specifier );
		}
		out = replace.call( out, RE_EXP_POS_DIGITS, 'e+0$1' );
		out = replace.call( out, RE_EXP_NEG_DIGITS, 'e-0$1' );
		if ( token.alternate ) {
			out = replace.call( out, RE_ONLY_DIGITS, '$1.' );
			out = replace.call( out, RE_DIGITS_BEFORE_EXP, '$1.e' );
		}
		if ( f >= 0 && token.sign ) {
			out = token.sign + out;
		}
		out = ( token.specifier === uppercase.call( token.specifier ) ) ?
			uppercase.call( out ) :
			lowercase.call( out );
		return out;
	}


	// EXPORTS //

	format_double = formatDouble;
	return format_double;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var space_pad;
var hasRequiredSpace_pad;

function requireSpace_pad () {
	if (hasRequiredSpace_pad) return space_pad;
	hasRequiredSpace_pad = 1;

	// FUNCTIONS //

	/**
	* Returns `n` spaces.
	*
	* @private
	* @param {number} n - number of spaces
	* @returns {string} string of spaces
	*/
	function spaces( n ) {
		var out = '';
		var i;
		for ( i = 0; i < n; i++ ) {
			out += ' ';
		}
		return out;
	}


	// MAIN //

	/**
	* Pads a token with spaces to the specified width.
	*
	* @private
	* @param {string} str - token argument
	* @param {number} width - token width
	* @param {boolean} [right=false] - boolean indicating whether to pad to the right
	* @returns {string} padded token argument
	*/
	function spacePad( str, width, right ) {
		var pad = width - str.length;
		if ( pad < 0 ) {
			return str;
		}
		str = ( right ) ?
			str + spaces( pad ) :
			spaces( pad ) + str;
		return str;
	}


	// EXPORTS //

	space_pad = spacePad;
	return space_pad;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$1e;
var hasRequiredMain$1e;

function requireMain$1e () {
	if (hasRequiredMain$1e) return main$1e;
	hasRequiredMain$1e = 1;

	// MODULES //

	var formatInteger = requireFormat_integer();
	var isString = requireIs_string$1();
	var formatDouble = requireFormat_double();
	var spacePad = requireSpace_pad();
	var zeroPad = requireZero_pad();


	// VARIABLES //

	var fromCharCode = String.fromCharCode;
	var isArray = Array.isArray; // NOTE: We use the global `Array.isArray` function here instead of `@stdlib/assert/is-array` to avoid circular dependencies.


	// FUNCTIONS //

	/**
	* Returns a boolean indicating whether a value is `NaN`.
	*
	* @private
	* @param {*} value - input value
	* @returns {boolean} boolean indicating whether a value is `NaN`
	*
	* @example
	* var bool = isnan( NaN );
	* // returns true
	*
	* @example
	* var bool = isnan( 4 );
	* // returns false
	*/
	function isnan( value ) { // explicitly define a function here instead of `@stdlib/math/base/assert/is-nan` in order to avoid circular dependencies
		return ( value !== value );
	}

	/**
	* Initializes token object with properties of supplied format identifier object or default values if not present.
	*
	* @private
	* @param {Object} token - format identifier object
	* @returns {Object} token object
	*/
	function initialize( token ) {
		var out = {};
		out.specifier = token.specifier;
		out.precision = ( token.precision === void 0 ) ? 1 : token.precision;
		out.width = token.width;
		out.flags = token.flags || '';
		out.mapping = token.mapping;
		return out;
	}


	// MAIN //

	/**
	* Generates string from a token array by interpolating values.
	*
	* @param {Array} tokens - string parts and format identifier objects
	* @param {Array} ...args - variable values
	* @throws {TypeError} first argument must be an array
	* @throws {Error} invalid flags
	* @returns {string} formatted string
	*
	* @example
	* var tokens = [ 'beep ', { 'specifier': 's' } ];
	* var out = formatInterpolate( tokens, 'boop' );
	* // returns 'beep boop'
	*/
	function formatInterpolate( tokens ) {
		var hasPeriod;
		var flags;
		var token;
		var flag;
		var num;
		var out;
		var pos;
		var i;
		var j;

		if ( !isArray( tokens ) ) {
			throw new TypeError( 'invalid argument. First argument must be an array. Value: `' + tokens + '`.' );
		}
		out = '';
		pos = 1;
		for ( i = 0; i < tokens.length; i++ ) {
			token = tokens[ i ];
			if ( isString( token ) ) {
				out += token;
			} else {
				hasPeriod = token.precision !== void 0;
				token = initialize( token );
				if ( !token.specifier ) {
					throw new TypeError( 'invalid argument. Token is missing `specifier` property. Index: `'+ i +'`. Value: `' + token + '`.' );
				}
				if ( token.mapping ) {
					pos = token.mapping;
				}
				flags = token.flags;
				for ( j = 0; j < flags.length; j++ ) {
					flag = flags.charAt( j );
					switch ( flag ) {
					case ' ':
						token.sign = ' ';
						break;
					case '+':
						token.sign = '+';
						break;
					case '-':
						token.padRight = true;
						token.padZeros = false;
						break;
					case '0':
						token.padZeros = flags.indexOf( '-' ) < 0; // NOTE: We use built-in `Array.prototype.indexOf` here instead of `@stdlib/assert/contains` in order to avoid circular dependencies.
						break;
					case '#':
						token.alternate = true;
						break;
					default:
						throw new Error( 'invalid flag: ' + flag );
					}
				}
				if ( token.width === '*' ) {
					token.width = parseInt( arguments[ pos ], 10 );
					pos += 1;
					if ( isnan( token.width ) ) {
						throw new TypeError( 'the argument for * width at position ' + pos + ' is not a number. Value: `' + token.width + '`.' );
					}
					if ( token.width < 0 ) {
						token.padRight = true;
						token.width = -token.width;
					}
				}
				if ( hasPeriod ) {
					if ( token.precision === '*' ) {
						token.precision = parseInt( arguments[ pos ], 10 );
						pos += 1;
						if ( isnan( token.precision ) ) {
							throw new TypeError( 'the argument for * precision at position ' + pos + ' is not a number. Value: `' + token.precision + '`.' );
						}
						if ( token.precision < 0 ) {
							token.precision = 1;
							hasPeriod = false;
						}
					}
				}
				token.arg = arguments[ pos ];
				switch ( token.specifier ) {
				case 'b':
				case 'o':
				case 'x':
				case 'X':
				case 'd':
				case 'i':
				case 'u':
					// Case: %b (binary), %o (octal), %x, %X (hexadecimal), %d, %i (decimal), %u (unsigned decimal)
					if ( hasPeriod ) {
						token.padZeros = false;
					}
					token.arg = formatInteger( token );
					break;
				case 's':
					// Case: %s (string)
					token.maxWidth = ( hasPeriod ) ? token.precision : -1;
					token.arg = String( token.arg );
					break;
				case 'c':
					// Case: %c (character)
					if ( !isnan( token.arg ) ) {
						num = parseInt( token.arg, 10 );
						if ( num < 0 || num > 127 ) {
							throw new Error( 'invalid character code. Value: ' + token.arg );
						}
						token.arg = ( isnan( num ) ) ? String( token.arg ) : fromCharCode( num ); // eslint-disable-line max-len
					}
					break;
				case 'e':
				case 'E':
				case 'f':
				case 'F':
				case 'g':
				case 'G':
					// Case: %e, %E (scientific notation), %f, %F (decimal floating point), %g, %G (uses the shorter of %e/E or %f/F)
					if ( !hasPeriod ) {
						token.precision = 6;
					}
					token.arg = formatDouble( token );
					break;
				default:
					throw new Error( 'invalid specifier: ' + token.specifier );
				}
				// Fit argument into field width...
				if ( token.maxWidth >= 0 && token.arg.length > token.maxWidth ) {
					token.arg = token.arg.substring( 0, token.maxWidth );
				}
				if ( token.padZeros ) {
					token.arg = zeroPad( token.arg, token.width || token.precision, token.padRight ); // eslint-disable-line max-len
				} else if ( token.width ) {
					token.arg = spacePad( token.arg, token.width, token.padRight );
				}
				out += token.arg || '';
				pos += 1;
			}
		}
		return out;
	}


	// EXPORTS //

	main$1e = formatInterpolate;
	return main$1e;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1y;
var hasRequiredLib$1y;

function requireLib$1y () {
	if (hasRequiredLib$1y) return lib$1y;
	hasRequiredLib$1y = 1;

	/**
	* Generate string from a token array by interpolating values.
	*
	* @module @stdlib/string-base-format-interpolate
	*
	* @example
	* var formatInterpolate = require( '@stdlib/string-base-format-interpolate' );
	*
	* var tokens = ['Hello ', { 'specifier': 's' }, '!' ];
	* var out = formatInterpolate( tokens, 'World' );
	* // returns 'Hello World!'
	*/

	// MODULES //

	var main = requireMain$1e();


	// EXPORTS //

	lib$1y = main;
	return lib$1y;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$1d;
var hasRequiredMain$1d;

function requireMain$1d () {
	if (hasRequiredMain$1d) return main$1d;
	hasRequiredMain$1d = 1;

	// VARIABLES //

	var RE = /%(?:([1-9]\d*)\$)?([0 +\-#]*)(\*|\d+)?(?:(\.)(\*|\d+)?)?[hlL]?([%A-Za-z])/g;


	// FUNCTIONS //

	/**
	* Parses a delimiter.
	*
	* @private
	* @param {Array} match - regular expression match
	* @returns {Object} delimiter token object
	*/
	function parse( match ) {
		var token = {
			'mapping': ( match[ 1 ] ) ? parseInt( match[ 1 ], 10 ) : void 0,
			'flags': match[ 2 ],
			'width': match[ 3 ],
			'precision': match[ 5 ],
			'specifier': match[ 6 ]
		};
		if ( match[ 4 ] === '.' && match[ 5 ] === void 0 ) {
			token.precision = '1';
		}
		return token;
	}


	// MAIN //

	/**
	* Tokenizes a string into an array of string parts and format identifier objects.
	*
	* @param {string} str - input string
	* @returns {Array} tokens
	*
	* @example
	* var tokens = formatTokenize( 'Hello %s!' );
	* // returns [ 'Hello ', {...}, '!' ]
	*/
	function formatTokenize( str ) {
		var content;
		var tokens;
		var match;
		var prev;

		tokens = [];
		prev = 0;
		match = RE.exec( str );
		while ( match ) {
			content = str.slice( prev, RE.lastIndex - match[ 0 ].length );
			if ( content.length ) {
				tokens.push( content );
			}
			tokens.push( parse( match ) );
			prev = RE.lastIndex;
			match = RE.exec( str );
		}
		content = str.slice( prev );
		if ( content.length ) {
			tokens.push( content );
		}
		return tokens;
	}


	// EXPORTS //

	main$1d = formatTokenize;
	return main$1d;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1x;
var hasRequiredLib$1x;

function requireLib$1x () {
	if (hasRequiredLib$1x) return lib$1x;
	hasRequiredLib$1x = 1;

	/**
	* Tokenize a string into an array of string parts and format identifier objects.
	*
	* @module @stdlib/string-base-format-tokenize
	*
	* @example
	* var formatTokenize = require( '@stdlib/string-base-format-tokenize' );
	*
	* var str = 'Hello %s!';
	* var tokens = formatTokenize( str );
	* // returns [ 'Hello ', {...}, '!' ]
	*/

	// MODULES //

	var main = requireMain$1d();


	// EXPORTS //

	lib$1x = main;
	return lib$1x;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var is_string;
var hasRequiredIs_string;

function requireIs_string () {
	if (hasRequiredIs_string) return is_string;
	hasRequiredIs_string = 1;

	/**
	* Tests if a value is a string primitive.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a string primitive
	*
	* @example
	* var bool = isString( 'beep' );
	* // returns true
	*
	* @example
	* var bool = isString( new String( 'beep' ) );
	* // returns false
	*/
	function isString( value ) {
		return ( typeof value === 'string' ); // NOTE: we inline the `isString.isPrimitive` function from `@stdlib/assert/is-string` in order to avoid circular dependencies.
	}


	// EXPORTS //

	is_string = isString;
	return is_string;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$1c;
var hasRequiredMain$1c;

function requireMain$1c () {
	if (hasRequiredMain$1c) return main$1c;
	hasRequiredMain$1c = 1;

	// MODULES //

	var interpolate = requireLib$1y();
	var tokenize = requireLib$1x();
	var isString = requireIs_string();


	// MAIN //

	/**
	* Inserts supplied variable values into a format string.
	*
	* @param {string} str - input string
	* @param {Array} ...args - variable values
	* @throws {TypeError} first argument must be a string
	* @throws {Error} invalid flags
	* @returns {string} formatted string
	*
	* @example
	* var str = format( 'Hello %s!', 'world' );
	* // returns 'Hello world!'
	*
	* @example
	* var str = format( 'Pi: ~%.2f', 3.141592653589793 );
	* // returns 'Pi: ~3.14'
	*/
	function format( str ) {
		var args;
		var i;

		if ( !isString( str ) ) {
			throw new TypeError( format( 'invalid argument. First argument must be a string. Value: `%s`.', str ) );
		}
		args = [ tokenize( str ) ];
		for ( i = 1; i < arguments.length; i++ ) {
			args.push( arguments[ i ] );
		}
		return interpolate.apply( null, args );
	}


	// EXPORTS //

	main$1c = format;
	return main$1c;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1w;
var hasRequiredLib$1w;

function requireLib$1w () {
	if (hasRequiredLib$1w) return lib$1w;
	hasRequiredLib$1w = 1;

	/**
	* Insert supplied variable values into a format string.
	*
	* @module @stdlib/string-format
	*
	* @example
	* var format = require( '@stdlib/string-format' );
	*
	* var out = format( '%s %s!', 'Hello', 'World' );
	* // returns 'Hello World!'
	*
	* out = format( 'Pi: ~%.2f', 3.141592653589793 );
	* // returns 'Pi: ~3.14'
	*/

	// MODULES //

	var main = requireMain$1c();


	// EXPORTS //

	lib$1w = main;
	return lib$1w;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyfill$1;
var hasRequiredPolyfill$5;

function requirePolyfill$5 () {
	if (hasRequiredPolyfill$5) return polyfill$1;
	hasRequiredPolyfill$5 = 1;

	// MODULES //

	var format = requireLib$1w();


	// VARIABLES //

	var objectProtoype = Object.prototype;
	var toStr = objectProtoype.toString;
	var defineGetter = objectProtoype.__defineGetter__;
	var defineSetter = objectProtoype.__defineSetter__;
	var lookupGetter = objectProtoype.__lookupGetter__;
	var lookupSetter = objectProtoype.__lookupSetter__;


	// MAIN //

	/**
	* Defines (or modifies) an object property.
	*
	* ## Notes
	*
	* -   Property descriptors come in two flavors: **data descriptors** and **accessor descriptors**. A data descriptor is a property that has a value, which may or may not be writable. An accessor descriptor is a property described by a getter-setter function pair. A descriptor must be one of these two flavors and cannot be both.
	*
	* @param {Object} obj - object on which to define the property
	* @param {string} prop - property name
	* @param {Object} descriptor - property descriptor
	* @param {boolean} [descriptor.configurable=false] - boolean indicating if property descriptor can be changed and if the property can be deleted from the provided object
	* @param {boolean} [descriptor.enumerable=false] - boolean indicating if the property shows up when enumerating object properties
	* @param {boolean} [descriptor.writable=false] - boolean indicating if the value associated with the property can be changed with an assignment operator
	* @param {*} [descriptor.value] - property value
	* @param {(Function|void)} [descriptor.get=undefined] - function which serves as a getter for the property, or, if no getter, undefined. When the property is accessed, a getter function is called without arguments and with the `this` context set to the object through which the property is accessed (which may not be the object on which the property is defined due to inheritance). The return value will be used as the property value.
	* @param {(Function|void)} [descriptor.set=undefined] - function which serves as a setter for the property, or, if no setter, undefined. When assigning a property value, a setter function is called with one argument (the value being assigned to the property) and with the `this` context set to the object through which the property is assigned.
	* @throws {TypeError} first argument must be an object
	* @throws {TypeError} third argument must be an object
	* @throws {Error} property descriptor cannot have both a value and a setter and/or getter
	* @returns {Object} object with added property
	*
	* @example
	* var obj = {};
	*
	* defineProperty( obj, 'foo', {
	*     'value': 'bar'
	* });
	*
	* var str = obj.foo;
	* // returns 'bar'
	*/
	function defineProperty( obj, prop, descriptor ) {
		var prototype;
		var hasValue;
		var hasGet;
		var hasSet;

		if ( typeof obj !== 'object' || obj === null || toStr.call( obj ) === '[object Array]' ) {
			throw new TypeError( format( 'invalid argument. First argument must be an object. Value: `%s`.', obj ) );
		}
		if ( typeof descriptor !== 'object' || descriptor === null || toStr.call( descriptor ) === '[object Array]' ) {
			throw new TypeError( format( 'invalid argument. Property descriptor must be an object. Value: `%s`.', descriptor ) );
		}
		hasValue = ( 'value' in descriptor );
		if ( hasValue ) {
			if (
				lookupGetter.call( obj, prop ) ||
				lookupSetter.call( obj, prop )
			) {
				// Override `__proto__` to avoid touching inherited accessors:
				prototype = obj.__proto__;
				obj.__proto__ = objectProtoype;

				// Delete property as existing getters/setters prevent assigning value to specified property:
				delete obj[ prop ];
				obj[ prop ] = descriptor.value;

				// Restore original prototype:
				obj.__proto__ = prototype;
			} else {
				obj[ prop ] = descriptor.value;
			}
		}
		hasGet = ( 'get' in descriptor );
		hasSet = ( 'set' in descriptor );

		if ( hasValue && ( hasGet || hasSet ) ) {
			throw new Error( 'invalid argument. Cannot specify one or more accessors and a value or writable attribute in the property descriptor.' );
		}

		if ( hasGet && defineGetter ) {
			defineGetter.call( obj, prop, descriptor.get );
		}
		if ( hasSet && defineSetter ) {
			defineSetter.call( obj, prop, descriptor.set );
		}
		return obj;
	}


	// EXPORTS //

	polyfill$1 = defineProperty;
	return polyfill$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1v;
var hasRequiredLib$1v;

function requireLib$1v () {
	if (hasRequiredLib$1v) return lib$1v;
	hasRequiredLib$1v = 1;

	/**
	* Define (or modify) an object property.
	*
	* @module @stdlib/utils-define-property
	*
	* @example
	* var defineProperty = require( '@stdlib/utils-define-property' );
	*
	* var obj = {};
	* defineProperty( obj, 'foo', {
	*     'value': 'bar',
	*     'writable': false,
	*     'configurable': false,
	*     'enumerable': false
	* });
	* obj.foo = 'boop'; // => throws
	*/

	// MODULES //

	var hasDefinePropertySupport = requireHas_define_property_support();
	var builtin = requireBuiltin();
	var polyfill = requirePolyfill$5();


	// MAIN //

	var defineProperty;
	if ( hasDefinePropertySupport() ) {
		defineProperty = builtin;
	} else {
		defineProperty = polyfill;
	}


	// EXPORTS //

	lib$1v = defineProperty;
	return lib$1v;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$1b;
var hasRequiredMain$1b;

function requireMain$1b () {
	if (hasRequiredMain$1b) return main$1b;
	hasRequiredMain$1b = 1;

	// MODULES //

	var defineProperty = requireLib$1v();


	// MAIN //

	/**
	* Defines a read-only property.
	*
	* @param {Object} obj - object on which to define the property
	* @param {(string|symbol)} prop - property name
	* @param {*} value - value to set
	*
	* @example
	* var obj = {};
	*
	* setReadOnly( obj, 'foo', 'bar' );
	*
	* try {
	*     obj.foo = 'boop';
	* } catch ( err ) {
	*     console.error( err.message );
	* }
	*/
	function setReadOnly( obj, prop, value ) {
		defineProperty( obj, prop, {
			'configurable': false,
			'enumerable': true,
			'writable': false,
			'value': value
		});
	}


	// EXPORTS //

	main$1b = setReadOnly;
	return main$1b;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1u;
var hasRequiredLib$1u;

function requireLib$1u () {
	if (hasRequiredLib$1u) return lib$1u;
	hasRequiredLib$1u = 1;

	/**
	* Define a read-only property.
	*
	* @module @stdlib/utils-define-read-only-property
	*
	* @example
	* var setReadOnly = require( '@stdlib/utils-define-read-only-property' );
	*
	* var obj = {};
	*
	* setReadOnly( obj, 'foo', 'bar' );
	*
	* try {
	*     obj.foo = 'boop';
	* } catch ( err ) {
	*     console.error( err.message );
	* }
	*/

	// MODULES //

	var main = requireMain$1b();


	// EXPORTS //

	lib$1u = main;
	return lib$1u;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$1a;
var hasRequiredMain$1a;

function requireMain$1a () {
	if (hasRequiredMain$1a) return main$1a;
	hasRequiredMain$1a = 1;

	// MODULES //

	var defineProperty = requireLib$1v();


	// MAIN //

	/**
	* Defines a non-enumerable read-only property.
	*
	* @param {Object} obj - object on which to define the property
	* @param {(string|symbol)} prop - property name
	* @param {*} value - value to set
	*
	* @example
	* var obj = {};
	*
	* setNonEnumerableReadOnly( obj, 'foo', 'bar' );
	*
	* try {
	*     obj.foo = 'boop';
	* } catch ( err ) {
	*     console.error( err.message );
	* }
	*/
	function setNonEnumerableReadOnly( obj, prop, value ) {
		defineProperty( obj, prop, {
			'configurable': false,
			'enumerable': false,
			'writable': false,
			'value': value
		});
	}


	// EXPORTS //

	main$1a = setNonEnumerableReadOnly;
	return main$1a;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1t;
var hasRequiredLib$1t;

function requireLib$1t () {
	if (hasRequiredLib$1t) return lib$1t;
	hasRequiredLib$1t = 1;

	/**
	* Define a non-enumerable read-only property.
	*
	* @module @stdlib/utils-define-nonenumerable-read-only-property
	*
	* @example
	* var setNonEnumerableReadOnly = require( '@stdlib/utils-define-nonenumerable-read-only-property' );
	*
	* var obj = {};
	*
	* setNonEnumerableReadOnly( obj, 'foo', 'bar' );
	*
	* try {
	*     obj.foo = 'boop';
	* } catch ( err ) {
	*     console.error( err.message );
	* }
	*/

	// MODULES //

	var main = requireMain$1a();


	// EXPORTS //

	lib$1t = main;
	return lib$1t;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$19;
var hasRequiredMain$19;

function requireMain$19 () {
	if (hasRequiredMain$19) return main$19;
	hasRequiredMain$19 = 1;

	// MAIN //

	/**
	* Tests if a double-precision floating-point numeric value is `NaN`.
	*
	* @param {number} x - value to test
	* @returns {boolean} boolean indicating whether the value is `NaN`
	*
	* @example
	* var bool = isnan( NaN );
	* // returns true
	*
	* @example
	* var bool = isnan( 7.0 );
	* // returns false
	*/
	function isnan( x ) {
		return ( x !== x );
	}


	// EXPORTS //

	main$19 = isnan;
	return main$19;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1s;
var hasRequiredLib$1s;

function requireLib$1s () {
	if (hasRequiredLib$1s) return lib$1s;
	hasRequiredLib$1s = 1;

	/**
	* Test if a double-precision floating-point numeric value is `NaN`.
	*
	* @module @stdlib/math-base-assert-is-nan
	*
	* @example
	* var isnan = require( '@stdlib/math-base-assert-is-nan' );
	*
	* var bool = isnan( NaN );
	* // returns true
	*
	* bool = isnan( 7.0 );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$19();


	// EXPORTS //

	lib$1s = main;
	return lib$1s;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$18;
var hasRequiredMain$18;

function requireMain$18 () {
	if (hasRequiredMain$18) return main$18;
	hasRequiredMain$18 = 1;

	// TODO: implementation (?)

	/**
	* Rounds a double-precision floating-point number toward negative infinity.
	*
	* @param {number} x - input value
	* @returns {number} rounded value
	*
	* @example
	* var v = floor( -4.2 );
	* // returns -5.0
	*
	* @example
	* var v = floor( 9.99999 );
	* // returns 9.0
	*
	* @example
	* var v = floor( 0.0 );
	* // returns 0.0
	*
	* @example
	* var v = floor( NaN );
	* // returns NaN
	*/
	var floor = Math.floor; // eslint-disable-line stdlib/no-builtin-math


	// EXPORTS //

	main$18 = floor;
	return main$18;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1r;
var hasRequiredLib$1r;

function requireLib$1r () {
	if (hasRequiredLib$1r) return lib$1r;
	hasRequiredLib$1r = 1;

	/**
	* Round a double-precision floating-point number toward negative infinity.
	*
	* @module @stdlib/math-base-special-floor
	*
	* @example
	* var floor = require( '@stdlib/math-base-special-floor' );
	*
	* var v = floor( -4.2 );
	* // returns -5.0
	*
	* v = floor( 9.99999 );
	* // returns 9.0
	*
	* v = floor( 0.0 );
	* // returns 0.0
	*
	* v = floor( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$18();


	// EXPORTS //

	lib$1r = main;
	return lib$1r;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$17;
var hasRequiredMain$17;

function requireMain$17 () {
	if (hasRequiredMain$17) return main$17;
	hasRequiredMain$17 = 1;

	// TODO: implementation (?)

	/**
	* Rounds a double-precision floating-point number toward positive infinity.
	*
	* @param {number} x - input value
	* @returns {number} rounded value
	*
	* @example
	* var v = ceil( -4.2 );
	* // returns -4.0
	*
	* @example
	* var v = ceil( 9.99999 );
	* // returns 10.0
	*
	* @example
	* var v = ceil( 0.0 );
	* // returns 0.0
	*
	* @example
	* var v = ceil( NaN );
	* // returns NaN
	*/
	var ceil = Math.ceil; // eslint-disable-line stdlib/no-builtin-math


	// EXPORTS //

	main$17 = ceil;
	return main$17;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1q;
var hasRequiredLib$1q;

function requireLib$1q () {
	if (hasRequiredLib$1q) return lib$1q;
	hasRequiredLib$1q = 1;

	/**
	* Round a double-precision floating-point number toward positive infinity.
	*
	* @module @stdlib/math-base-special-ceil
	*
	* @example
	* var ceil = require( '@stdlib/math-base-special-ceil' );
	*
	* var v = ceil( -4.2 );
	* // returns -4.0
	*
	* v = ceil( 9.99999 );
	* // returns 10.0
	*
	* v = ceil( 0.0 );
	* // returns 0.0
	*
	* v = ceil( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$17();


	// EXPORTS //

	lib$1q = main;
	return lib$1q;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$16;
var hasRequiredMain$16;

function requireMain$16 () {
	if (hasRequiredMain$16) return main$16;
	hasRequiredMain$16 = 1;

	// MODULES //

	var floor = requireLib$1r();
	var ceil = requireLib$1q();


	// MAIN //

	/**
	* Rounds a double-precision floating-point number toward zero.
	*
	* @param {number} x - input value
	* @returns {number} rounded value
	*
	* @example
	* var v = trunc( -4.2 );
	* // returns -4.0
	*
	* @example
	* var v = trunc( 9.99999 );
	* // returns 9.0
	*
	* @example
	* var v = trunc( 0.0 );
	* // returns 0.0
	*
	* @example
	* var v = trunc( -0.0 );
	* // returns -0.0
	*
	* @example
	* var v = trunc( NaN );
	* // returns NaN
	*
	* @example
	* var v = trunc( Infinity );
	* // returns Infinity
	*
	* @example
	* var v = trunc( -Infinity );
	* // returns -Infinity
	*/
	function trunc( x ) {
		if ( x < 0.0 ) {
			return ceil( x );
		}
		return floor( x );
	}


	// EXPORTS //

	main$16 = trunc;
	return main$16;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1p;
var hasRequiredLib$1p;

function requireLib$1p () {
	if (hasRequiredLib$1p) return lib$1p;
	hasRequiredLib$1p = 1;

	/**
	* Round a double-precision floating-point number toward zero.
	*
	* @module @stdlib/math-base-special-trunc
	*
	* @example
	* var trunc = require( '@stdlib/math-base-special-trunc' );
	*
	* var v = trunc( -4.2 );
	* // returns -4.0
	*
	* v = trunc( 9.99999 );
	* // returns 9.0
	*
	* v = trunc( 0.0 );
	* // returns 0.0
	*
	* v = trunc( -0.0 );
	* // returns -0.0
	*
	* v = trunc( NaN );
	* // returns NaN
	*
	* v = trunc( Infinity );
	* // returns Infinity
	*
	* v = trunc( -Infinity );
	* // returns -Infinity
	*/

	// MODULES //

	var main = requireMain$16();


	// EXPORTS //

	lib$1p = main;
	return lib$1p;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$15;
var hasRequiredMain$15;

function requireMain$15 () {
	if (hasRequiredMain$15) return main$15;
	hasRequiredMain$15 = 1;

	// EXPORTS //

	main$15 = Number; // eslint-disable-line stdlib/require-globals
	return main$15;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1o;
var hasRequiredLib$1o;

function requireLib$1o () {
	if (hasRequiredLib$1o) return lib$1o;
	hasRequiredLib$1o = 1;

	/**
	* Constructor which returns a `Number` object.
	*
	* @module @stdlib/number-ctor
	*
	* @example
	* var Number = require( '@stdlib/number-ctor' );
	*
	* var v = new Number( 10.0 );
	* // returns <Number>
	*/

	// MODULES //

	var main = requireMain$15();


	// EXPORTS //

	lib$1o = main;
	return lib$1o;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1n;
var hasRequiredLib$1n;

function requireLib$1n () {
	if (hasRequiredLib$1n) return lib$1n;
	hasRequiredLib$1n = 1;

	/**
	* Double-precision floating-point negative infinity.
	*
	* @module @stdlib/constants-float64-ninf
	* @type {number}
	*
	* @example
	* var FLOAT64_NINF = require( '@stdlib/constants-float64-ninf' );
	* // returns -Infinity
	*/

	// MODULES //

	var Number = requireLib$1o();


	// MAIN //

	/**
	* Double-precision floating-point negative infinity.
	*
	* ## Notes
	*
	* Double-precision floating-point negative infinity has the bit sequence
	*
	* ```binarystring
	* 1 11111111111 00000000000000000000 00000000000000000000000000000000
	* ```
	*
	* @constant
	* @type {number}
	* @default Number.NEGATIVE_INFINITY
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_NINF = Number.NEGATIVE_INFINITY;


	// EXPORTS //

	lib$1n = FLOAT64_NINF;
	return lib$1n;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1m;
var hasRequiredLib$1m;

function requireLib$1m () {
	if (hasRequiredLib$1m) return lib$1m;
	hasRequiredLib$1m = 1;

	/**
	* Double-precision floating-point positive infinity.
	*
	* @module @stdlib/constants-float64-pinf
	* @type {number}
	*
	* @example
	* var FLOAT64_PINF = require( '@stdlib/constants-float64-pinf' );
	* // returns Infinity
	*/


	// MAIN //

	/**
	* Double-precision floating-point positive infinity.
	*
	* ## Notes
	*
	* Double-precision floating-point positive infinity has the bit sequence
	*
	* ```binarystring
	* 0 11111111111 00000000000000000000 00000000000000000000000000000000
	* ```
	*
	* @constant
	* @type {number}
	* @default Number.POSITIVE_INFINITY
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_PINF = Number.POSITIVE_INFINITY; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	lib$1m = FLOAT64_PINF;
	return lib$1m;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1l;
var hasRequiredLib$1l;

function requireLib$1l () {
	if (hasRequiredLib$1l) return lib$1l;
	hasRequiredLib$1l = 1;

	/**
	* The bias of a double-precision floating-point number's exponent.
	*
	* @module @stdlib/constants-float64-exponent-bias
	* @type {integer32}
	*
	* @example
	* var FLOAT64_EXPONENT_BIAS = require( '@stdlib/constants-float64-exponent-bias' );
	* // returns 1023
	*/


	// MAIN //

	/**
	* Bias of a double-precision floating-point number's exponent.
	*
	* ## Notes
	*
	* The bias can be computed via
	*
	* ```tex
	* \mathrm{bias} = 2^{k-1} - 1
	* ```
	*
	* where \\(k\\) is the number of bits in the exponent; here, \\(k = 11\\).
	*
	* @constant
	* @type {integer32}
	* @default 1023
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_EXPONENT_BIAS = 1023|0; // asm type annotation


	// EXPORTS //

	lib$1l = FLOAT64_EXPONENT_BIAS;
	return lib$1l;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1k;
var hasRequiredLib$1k;

function requireLib$1k () {
	if (hasRequiredLib$1k) return lib$1k;
	hasRequiredLib$1k = 1;

	/**
	* The maximum biased base 2 exponent for a double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-max-base2-exponent
	* @type {integer32}
	*
	* @example
	* var FLOAT64_MAX_BASE2_EXPONENT = require( '@stdlib/constants-float64-max-base2-exponent' );
	* // returns 1023
	*/


	// MAIN //

	/**
	* The maximum biased base 2 exponent for a double-precision floating-point number.
	*
	* ```text
	* 11111111110 => 2046 - BIAS = 1023
	* ```
	*
	* where `BIAS = 1023`.
	*
	* @constant
	* @type {integer32}
	* @default 1023
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_MAX_BASE2_EXPONENT = 1023|0; // asm type annotation


	// EXPORTS //

	lib$1k = FLOAT64_MAX_BASE2_EXPONENT;
	return lib$1k;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1j;
var hasRequiredLib$1j;

function requireLib$1j () {
	if (hasRequiredLib$1j) return lib$1j;
	hasRequiredLib$1j = 1;

	/**
	* The maximum biased base 2 exponent for a subnormal double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-max-base2-exponent-subnormal
	* @type {integer32}
	*
	* @example
	* var FLOAT64_MAX_BASE2_EXPONENT_SUBNORMAL = require( '@stdlib/constants-float64-max-base2-exponent-subnormal' );
	* // returns -1023
	*/


	// MAIN //

	/**
	* The maximum biased base 2 exponent for a subnormal double-precision floating-point number.
	*
	* ```text
	* 00000000000 => 0 - BIAS = -1023
	* ```
	*
	* where `BIAS = 1023`.
	*
	* @constant
	* @type {integer32}
	* @default -1023
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_MAX_BASE2_EXPONENT_SUBNORMAL = -1023|0; // asm type annotation


	// EXPORTS //

	lib$1j = FLOAT64_MAX_BASE2_EXPONENT_SUBNORMAL;
	return lib$1j;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1i;
var hasRequiredLib$1i;

function requireLib$1i () {
	if (hasRequiredLib$1i) return lib$1i;
	hasRequiredLib$1i = 1;

	/**
	* The minimum biased base 2 exponent for a subnormal double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-min-base2-exponent-subnormal
	* @type {integer32}
	*
	* @example
	* var FLOAT64_MIN_BASE2_EXPONENT_SUBNORMAL = require( '@stdlib/constants-float64-min-base2-exponent-subnormal' );
	* // returns -1074
	*/


	// MAIN //

	/**
	* The minimum biased base 2 exponent for a subnormal double-precision floating-point number.
	*
	* ```text
	* -(BIAS+(52-1)) = -(1023+51) = -1074
	* ```
	*
	* where `BIAS = 1023` and `52` is the number of digits in the significand.
	*
	* @constant
	* @type {integer32}
	* @default -1074
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_MIN_BASE2_EXPONENT_SUBNORMAL = -1074|0; // asm type annotation


	// EXPORTS //

	lib$1i = FLOAT64_MIN_BASE2_EXPONENT_SUBNORMAL;
	return lib$1i;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$14;
var hasRequiredMain$14;

function requireMain$14 () {
	if (hasRequiredMain$14) return main$14;
	hasRequiredMain$14 = 1;

	// MODULES //

	var PINF = requireLib$1m();
	var NINF = requireLib$1n();


	// MAIN //

	/**
	* Tests if a double-precision floating-point numeric value is infinite.
	*
	* @param {number} x - value to test
	* @returns {boolean} boolean indicating whether the value is infinite
	*
	* @example
	* var bool = isInfinite( Infinity );
	* // returns true
	*
	* @example
	* var bool = isInfinite( -Infinity );
	* // returns true
	*
	* @example
	* var bool = isInfinite( 5.0 );
	* // returns false
	*
	* @example
	* var bool = isInfinite( NaN );
	* // returns false
	*/
	function isInfinite( x ) {
		return (x === PINF || x === NINF);
	}


	// EXPORTS //

	main$14 = isInfinite;
	return main$14;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1h;
var hasRequiredLib$1h;

function requireLib$1h () {
	if (hasRequiredLib$1h) return lib$1h;
	hasRequiredLib$1h = 1;

	/**
	* Test if a double-precision floating-point numeric value is infinite.
	*
	* @module @stdlib/math-base-assert-is-infinite
	*
	* @example
	* var isInfinite = require( '@stdlib/math-base-assert-is-infinite' );
	*
	* var bool = isInfinite( Infinity );
	* // returns true
	*
	* bool = isInfinite( -Infinity );
	* // returns true
	*
	* bool = isInfinite( 5.0 );
	* // returns false
	*
	* bool = isInfinite( NaN );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$14();


	// EXPORTS //

	lib$1h = main;
	return lib$1h;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1g;
var hasRequiredLib$1g;

function requireLib$1g () {
	if (hasRequiredLib$1g) return lib$1g;
	hasRequiredLib$1g = 1;

	/**
	* High word mask for the sign bit of a double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-high-word-sign-mask
	* @type {uinteger32}
	*
	* @example
	* var FLOAT64_HIGH_WORD_SIGN_MASK = require( '@stdlib/constants-float64-high-word-sign-mask' );
	* // returns 2147483648
	*/


	// MAIN //

	/**
	* High word mask for the sign bit of a double-precision floating-point number.
	*
	* ## Notes
	*
	* The high word mask for the sign bit of a double-precision floating-point number is an unsigned 32-bit integer with the value \\( 2147483648 \\), which corresponds to the bit sequence
	*
	* ```binarystring
	* 1 00000000000 00000000000000000000
	* ```
	*
	* @constant
	* @type {uinteger32}
	* @default 0x80000000
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_HIGH_WORD_SIGN_MASK = 0x80000000>>>0; // eslint-disable-line id-length


	// EXPORTS //

	lib$1g = FLOAT64_HIGH_WORD_SIGN_MASK;
	return lib$1g;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1f;
var hasRequiredLib$1f;

function requireLib$1f () {
	if (hasRequiredLib$1f) return lib$1f;
	hasRequiredLib$1f = 1;

	/**
	* High word mask for excluding the sign bit of a double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-high-word-abs-mask
	* @type {uinteger32}
	*
	* @example
	* var FLOAT64_HIGH_WORD_ABS_MASK = require( '@stdlib/constants-float64-high-word-abs-mask' );
	* // returns 2147483647
	*/


	// MAIN //

	/**
	* High word mask for excluding the sign bit of a double-precision floating-point number.
	*
	* ## Notes
	*
	* The high word mask for excluding the sign bit of a double-precision floating-point number is an unsigned 32-bit integer with the value \\( 2147483647 \\), which corresponds to the bit sequence
	*
	* ```binarystring
	* 0 11111111111 11111111111111111111
	* ```
	*
	* @constant
	* @type {uinteger32}
	* @default 0x7fffffff
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_HIGH_WORD_ABS_MASK = 0x7fffffff>>>0; // eslint-disable-line id-length


	// EXPORTS //

	lib$1f = FLOAT64_HIGH_WORD_ABS_MASK;
	return lib$1f;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$13;
var hasRequiredMain$13;

function requireMain$13 () {
	if (hasRequiredMain$13) return main$13;
	hasRequiredMain$13 = 1;

	// MAIN //

	/**
	* Tests for native `Symbol` support.
	*
	* @returns {boolean} boolean indicating if an environment has `Symbol` support
	*
	* @example
	* var bool = hasSymbolSupport();
	* // returns <boolean>
	*/
	function hasSymbolSupport() {
		return (
			typeof Symbol === 'function' &&
			typeof Symbol( 'foo' ) === 'symbol'
		);
	}


	// EXPORTS //

	main$13 = hasSymbolSupport;
	return main$13;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1e;
var hasRequiredLib$1e;

function requireLib$1e () {
	if (hasRequiredLib$1e) return lib$1e;
	hasRequiredLib$1e = 1;

	/**
	* Test for native `Symbol` support.
	*
	* @module @stdlib/assert-has-symbol-support
	*
	* @example
	* var hasSymbolSupport = require( '@stdlib/assert-has-symbol-support' );
	*
	* var bool = hasSymbolSupport();
	* // returns <boolean>
	*/

	// MODULES //

	var main = requireMain$13();


	// EXPORTS //

	lib$1e = main;
	return lib$1e;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$12;
var hasRequiredMain$12;

function requireMain$12 () {
	if (hasRequiredMain$12) return main$12;
	hasRequiredMain$12 = 1;

	// MODULES //

	var hasSymbols = requireLib$1e();


	// VARIABLES //

	var FLG = hasSymbols();


	// MAIN //

	/**
	* Tests for native `toStringTag` support.
	*
	* @returns {boolean} boolean indicating if an environment has `toStringTag` support
	*
	* @example
	* var bool = hasToStringTagSupport();
	* // returns <boolean>
	*/
	function hasToStringTagSupport() {
		return ( FLG && typeof Symbol.toStringTag === 'symbol' );
	}


	// EXPORTS //

	main$12 = hasToStringTagSupport;
	return main$12;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1d;
var hasRequiredLib$1d;

function requireLib$1d () {
	if (hasRequiredLib$1d) return lib$1d;
	hasRequiredLib$1d = 1;

	/**
	* Test for native `toStringTag` support.
	*
	* @module @stdlib/assert-has-tostringtag-support
	*
	* @example
	* var hasToStringTagSupport = require( '@stdlib/assert-has-tostringtag-support' );
	*
	* var bool = hasToStringTagSupport();
	* // returns <boolean>
	*/

	// MODULES //

	var main = requireMain$12();


	// EXPORTS //

	lib$1d = main;
	return lib$1d;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var tostring$1;
var hasRequiredTostring$1;

function requireTostring$1 () {
	if (hasRequiredTostring$1) return tostring$1;
	hasRequiredTostring$1 = 1;

	// MAIN //

	var toStr = Object.prototype.toString;


	// EXPORTS //

	tostring$1 = toStr;
	return tostring$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$11;
var hasRequiredMain$11;

function requireMain$11 () {
	if (hasRequiredMain$11) return main$11;
	hasRequiredMain$11 = 1;

	// MODULES //

	var toStr = requireTostring$1();


	// MAIN //

	/**
	* Returns a string value indicating a specification defined classification (via the internal property `[[Class]]`) of an object.
	*
	* @param {*} v - input value
	* @returns {string} string value indicating a specification defined classification of the input value
	*
	* @example
	* var str = nativeClass( 'a' );
	* // returns '[object String]'
	*
	* @example
	* var str = nativeClass( 5 );
	* // returns '[object Number]'
	*
	* @example
	* function Beep() {
	*     return this;
	* }
	* var str = nativeClass( new Beep() );
	* // returns '[object Object]'
	*/
	function nativeClass( v ) {
		return toStr.call( v );
	}


	// EXPORTS //

	main$11 = nativeClass;
	return main$11;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$10;
var hasRequiredMain$10;

function requireMain$10 () {
	if (hasRequiredMain$10) return main$10;
	hasRequiredMain$10 = 1;

	// FUNCTIONS //

	var has = Object.prototype.hasOwnProperty;


	// MAIN //

	/**
	* Tests if an object has a specified property.
	*
	* @param {*} value - value to test
	* @param {*} property - property to test
	* @returns {boolean} boolean indicating if an object has a specified property
	*
	* @example
	* var beep = {
	*     'boop': true
	* };
	*
	* var bool = hasOwnProp( beep, 'boop' );
	* // returns true
	*
	* @example
	* var beep = {
	*     'boop': true
	* };
	*
	* var bool = hasOwnProp( beep, 'bap' );
	* // returns false
	*/
	function hasOwnProp( value, property ) {
		if (
			value === void 0 ||
			value === null
		) {
			return false;
		}
		return has.call( value, property );
	}


	// EXPORTS //

	main$10 = hasOwnProp;
	return main$10;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1c;
var hasRequiredLib$1c;

function requireLib$1c () {
	if (hasRequiredLib$1c) return lib$1c;
	hasRequiredLib$1c = 1;

	/**
	* Test whether an object has a specified property.
	*
	* @module @stdlib/assert-has-own-property
	*
	* @example
	* var hasOwnProp = require( '@stdlib/assert-has-own-property' );
	*
	* var beep = {
	*     'boop': true
	* };
	*
	* var bool = hasOwnProp( beep, 'boop' );
	* // returns true
	*
	* bool = hasOwnProp( beep, 'bop' );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$10();


	// EXPORTS //

	lib$1c = main;
	return lib$1c;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$$;
var hasRequiredMain$$;

function requireMain$$ () {
	if (hasRequiredMain$$) return main$$;
	hasRequiredMain$$ = 1;

	// MAIN //

	var Sym = ( typeof Symbol === 'function' ) ? Symbol : void 0; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	main$$ = Sym;
	return main$$;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1b;
var hasRequiredLib$1b;

function requireLib$1b () {
	if (hasRequiredLib$1b) return lib$1b;
	hasRequiredLib$1b = 1;

	/**
	* Symbol factory.
	*
	* @module @stdlib/symbol-ctor
	*
	* @example
	* var Symbol = require( '@stdlib/symbol-ctor' );
	*
	* var s = Symbol( 'beep' );
	* // returns <symbol>
	*/

	// MODULES //

	var main = requireMain$$();


	// EXPORTS //

	lib$1b = main;
	return lib$1b;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var tostringtag;
var hasRequiredTostringtag;

function requireTostringtag () {
	if (hasRequiredTostringtag) return tostringtag;
	hasRequiredTostringtag = 1;

	// MODULES //

	var Symbol = requireLib$1b();


	// MAIN //

	var toStrTag = ( typeof Symbol === 'function' ) ? Symbol.toStringTag : '';


	// EXPORTS //

	tostringtag = toStrTag;
	return tostringtag;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyfill;
var hasRequiredPolyfill$4;

function requirePolyfill$4 () {
	if (hasRequiredPolyfill$4) return polyfill;
	hasRequiredPolyfill$4 = 1;

	// MODULES //

	var hasOwnProp = requireLib$1c();
	var toStringTag = requireTostringtag();
	var toStr = requireTostring$1();


	// MAIN //

	/**
	* Returns a string value indicating a specification defined classification of an object in environments supporting `Symbol.toStringTag`.
	*
	* @param {*} v - input value
	* @returns {string} string value indicating a specification defined classification of the input value
	*
	* @example
	* var str = nativeClass( 'a' );
	* // returns '[object String]'
	*
	* @example
	* var str = nativeClass( 5 );
	* // returns '[object Number]'
	*
	* @example
	* function Beep() {
	*     return this;
	* }
	* var str = nativeClass( new Beep() );
	* // returns '[object Object]'
	*/
	function nativeClass( v ) {
		var isOwn;
		var tag;
		var out;

		if ( v === null || v === void 0 ) {
			return toStr.call( v );
		}
		tag = v[ toStringTag ];
		isOwn = hasOwnProp( v, toStringTag );

		// Attempt to override the `toStringTag` property. For built-ins having a `Symbol.toStringTag` property (e.g., `JSON`, `Math`, etc), the `Symbol.toStringTag` property is read-only (e.g., , so we need to wrap in a `try/catch`.
		try {
			v[ toStringTag ] = void 0;
		} catch ( err ) { // eslint-disable-line no-unused-vars
			return toStr.call( v );
		}
		out = toStr.call( v );

		if ( isOwn ) {
			v[ toStringTag ] = tag;
		} else {
			delete v[ toStringTag ];
		}
		return out;
	}


	// EXPORTS //

	polyfill = nativeClass;
	return polyfill;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1a;
var hasRequiredLib$1a;

function requireLib$1a () {
	if (hasRequiredLib$1a) return lib$1a;
	hasRequiredLib$1a = 1;

	/**
	* Return a string value indicating a specification defined classification of an object.
	*
	* @module @stdlib/utils-native-class
	*
	* @example
	* var nativeClass = require( '@stdlib/utils-native-class' );
	*
	* var str = nativeClass( 'a' );
	* // returns '[object String]'
	*
	* str = nativeClass( 5 );
	* // returns '[object Number]'
	*
	* function Beep() {
	*     return this;
	* }
	* str = nativeClass( new Beep() );
	* // returns '[object Object]'
	*/

	// MODULES //

	var hasToStringTag = requireLib$1d();
	var builtin = requireMain$11();
	var polyfill = requirePolyfill$4();


	// MAIN //

	var main;
	if ( hasToStringTag() ) {
		main = polyfill;
	} else {
		main = builtin;
	}


	// EXPORTS //

	lib$1a = main;
	return lib$1a;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$_;
var hasRequiredMain$_;

function requireMain$_ () {
	if (hasRequiredMain$_) return main$_;
	hasRequiredMain$_ = 1;

	// MODULES //

	var nativeClass = requireLib$1a();


	// VARIABLES //

	var hasUint32Array = ( typeof Uint32Array === 'function' ); // eslint-disable-line stdlib/require-globals


	// MAIN //

	/**
	* Tests if a value is a Uint32Array.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is a Uint32Array
	*
	* @example
	* var bool = isUint32Array( new Uint32Array( 10 ) );
	* // returns true
	*
	* @example
	* var bool = isUint32Array( [] );
	* // returns false
	*/
	function isUint32Array( value ) {
		return (
			( hasUint32Array && value instanceof Uint32Array ) || // eslint-disable-line stdlib/require-globals
			nativeClass( value ) === '[object Uint32Array]'
		);
	}


	// EXPORTS //

	main$_ = isUint32Array;
	return main$_;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$19;
var hasRequiredLib$19;

function requireLib$19 () {
	if (hasRequiredLib$19) return lib$19;
	hasRequiredLib$19 = 1;

	/**
	* Test if a value is a Uint32Array.
	*
	* @module @stdlib/assert-is-uint32array
	*
	* @example
	* var isUint32Array = require( '@stdlib/assert-is-uint32array' );
	*
	* var bool = isUint32Array( new Uint32Array( 10 ) );
	* // returns true
	*
	* bool = isUint32Array( [] );
	* // returns false
	*/

	// MODULES //

	var isUint32Array = requireMain$_();


	// EXPORTS //

	lib$19 = isUint32Array;
	return lib$19;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$18;
var hasRequiredLib$18;

function requireLib$18 () {
	if (hasRequiredLib$18) return lib$18;
	hasRequiredLib$18 = 1;

	/**
	* Maximum unsigned 32-bit integer.
	*
	* @module @stdlib/constants-uint32-max
	* @type {uinteger32}
	*
	* @example
	* var UINT32_MAX = require( '@stdlib/constants-uint32-max' );
	* // returns 4294967295
	*/


	// MAIN //

	/**
	* Maximum unsigned 32-bit integer.
	*
	* ## Notes
	*
	* The number has the value
	*
	* ```tex
	* 2^{32} - 1
	* ```
	*
	* which corresponds to the bit sequence
	*
	* ```binarystring
	* 11111111111111111111111111111111
	* ```
	*
	* @constant
	* @type {uinteger32}
	* @default 4294967295
	*/
	var UINT32_MAX = 4294967295;


	// EXPORTS //

	lib$18 = UINT32_MAX;
	return lib$18;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var uint32array;
var hasRequiredUint32array;

function requireUint32array () {
	if (hasRequiredUint32array) return uint32array;
	hasRequiredUint32array = 1;

	// MAIN //

	var main = ( typeof Uint32Array === 'function' ) ? Uint32Array : null; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	uint32array = main;
	return uint32array;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$Z;
var hasRequiredMain$Z;

function requireMain$Z () {
	if (hasRequiredMain$Z) return main$Z;
	hasRequiredMain$Z = 1;

	// MODULES //

	var isUint32Array = requireLib$19();
	var UINT32_MAX = requireLib$18();
	var GlobalUint32Array = requireUint32array();


	// MAIN //

	/**
	* Tests for native `Uint32Array` support.
	*
	* @returns {boolean} boolean indicating if an environment has `Uint32Array` support
	*
	* @example
	* var bool = hasUint32ArraySupport();
	* // returns <boolean>
	*/
	function hasUint32ArraySupport() {
		var bool;
		var arr;

		if ( typeof GlobalUint32Array !== 'function' ) {
			return false;
		}
		// Test basic support...
		try {
			arr = [ 1, 3.14, -3.14, UINT32_MAX+1, UINT32_MAX+2 ];
			arr = new GlobalUint32Array( arr );
			bool = (
				isUint32Array( arr ) &&
				arr[ 0 ] === 1 &&
				arr[ 1 ] === 3 &&            // truncation
				arr[ 2 ] === UINT32_MAX-2 && // truncation and wrap around
				arr[ 3 ] === 0 &&            // wrap around
				arr[ 4 ] === 1               // wrap around
			);
		} catch ( err ) { // eslint-disable-line no-unused-vars
			bool = false;
		}
		return bool;
	}


	// EXPORTS //

	main$Z = hasUint32ArraySupport;
	return main$Z;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$17;
var hasRequiredLib$17;

function requireLib$17 () {
	if (hasRequiredLib$17) return lib$17;
	hasRequiredLib$17 = 1;

	/**
	* Test for native `Uint32Array` support.
	*
	* @module @stdlib/assert-has-uint32array-support
	*
	* @example
	* var hasUint32ArraySupport = require( '@stdlib/assert-has-uint32array-support' );
	*
	* var bool = hasUint32ArraySupport();
	* // returns <boolean>
	*/

	// MODULES //

	var hasUint32ArraySupport = requireMain$Z();


	// EXPORTS //

	lib$17 = hasUint32ArraySupport;
	return lib$17;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$Y;
var hasRequiredMain$Y;

function requireMain$Y () {
	if (hasRequiredMain$Y) return main$Y;
	hasRequiredMain$Y = 1;

	// MAIN //

	var ctor = ( typeof Uint32Array === 'function' ) ? Uint32Array : void 0; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	main$Y = ctor;
	return main$Y;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyfill_1$3;
var hasRequiredPolyfill$3;

function requirePolyfill$3 () {
	if (hasRequiredPolyfill$3) return polyfill_1$3;
	hasRequiredPolyfill$3 = 1;

	// TODO: write polyfill

	// MAIN //

	/**
	* Typed array which represents an array of 32-bit unsigned integers in the platform byte order.
	*
	* @throws {Error} not implemented
	*/
	function polyfill() {
		throw new Error( 'not implemented' );
	}


	// EXPORTS //

	polyfill_1$3 = polyfill;
	return polyfill_1$3;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$16;
var hasRequiredLib$16;

function requireLib$16 () {
	if (hasRequiredLib$16) return lib$16;
	hasRequiredLib$16 = 1;

	/**
	* Typed array constructor which returns a typed array representing an array of 32-bit unsigned integers in the platform byte order.
	*
	* @module @stdlib/array-uint32
	*
	* @example
	* var ctor = require( '@stdlib/array-uint32' );
	*
	* var arr = new ctor( 10 );
	* // returns <Uint32Array>
	*/

	// MODULES //

	var hasUint32ArraySupport = requireLib$17();
	var builtin = requireMain$Y();
	var polyfill = requirePolyfill$3();


	// MAIN //

	var ctor;
	if ( hasUint32ArraySupport() ) {
		ctor = builtin;
	} else {
		ctor = polyfill;
	}


	// EXPORTS //

	lib$16 = ctor;
	return lib$16;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$X;
var hasRequiredMain$X;

function requireMain$X () {
	if (hasRequiredMain$X) return main$X;
	hasRequiredMain$X = 1;

	// MODULES //

	var nativeClass = requireLib$1a();


	// VARIABLES //

	var hasFloat64Array = ( typeof Float64Array === 'function' ); // eslint-disable-line stdlib/require-globals


	// MAIN //

	/**
	* Tests if a value is a Float64Array.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is a Float64Array
	*
	* @example
	* var bool = isFloat64Array( new Float64Array( 10 ) );
	* // returns true
	*
	* @example
	* var bool = isFloat64Array( [] );
	* // returns false
	*/
	function isFloat64Array( value ) {
		return (
			( hasFloat64Array && value instanceof Float64Array ) || // eslint-disable-line stdlib/require-globals
			nativeClass( value ) === '[object Float64Array]'
		);
	}


	// EXPORTS //

	main$X = isFloat64Array;
	return main$X;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$15;
var hasRequiredLib$15;

function requireLib$15 () {
	if (hasRequiredLib$15) return lib$15;
	hasRequiredLib$15 = 1;

	/**
	* Test if a value is a Float64Array.
	*
	* @module @stdlib/assert-is-float64array
	*
	* @example
	* var isFloat64Array = require( '@stdlib/assert-is-float64array' );
	*
	* var bool = isFloat64Array( new Float64Array( 10 ) );
	* // returns true
	*
	* bool = isFloat64Array( [] );
	* // returns false
	*/

	// MODULES //

	var isFloat64Array = requireMain$X();


	// EXPORTS //

	lib$15 = isFloat64Array;
	return lib$15;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var float64array;
var hasRequiredFloat64array;

function requireFloat64array () {
	if (hasRequiredFloat64array) return float64array;
	hasRequiredFloat64array = 1;

	// MAIN //

	var main = ( typeof Float64Array === 'function' ) ? Float64Array : null; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	float64array = main;
	return float64array;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$W;
var hasRequiredMain$W;

function requireMain$W () {
	if (hasRequiredMain$W) return main$W;
	hasRequiredMain$W = 1;

	// MODULES //

	var isFloat64Array = requireLib$15();
	var GlobalFloat64Array = requireFloat64array();


	// MAIN //

	/**
	* Tests for native `Float64Array` support.
	*
	* @returns {boolean} boolean indicating if an environment has `Float64Array` support
	*
	* @example
	* var bool = hasFloat64ArraySupport();
	* // returns <boolean>
	*/
	function hasFloat64ArraySupport() {
		var bool;
		var arr;

		if ( typeof GlobalFloat64Array !== 'function' ) {
			return false;
		}
		// Test basic support...
		try {
			arr = new GlobalFloat64Array( [ 1.0, 3.14, -3.14, NaN ] );
			bool = (
				isFloat64Array( arr ) &&
				arr[ 0 ] === 1.0 &&
				arr[ 1 ] === 3.14 &&
				arr[ 2 ] === -3.14 &&
				arr[ 3 ] !== arr[ 3 ]
			);
		} catch ( err ) { // eslint-disable-line no-unused-vars
			bool = false;
		}
		return bool;
	}


	// EXPORTS //

	main$W = hasFloat64ArraySupport;
	return main$W;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$14;
var hasRequiredLib$14;

function requireLib$14 () {
	if (hasRequiredLib$14) return lib$14;
	hasRequiredLib$14 = 1;

	/**
	* Test for native `Float64Array` support.
	*
	* @module @stdlib/assert-has-float64array-support
	*
	* @example
	* var hasFloat64ArraySupport = require( '@stdlib/assert-has-float64array-support' );
	*
	* var bool = hasFloat64ArraySupport();
	* // returns <boolean>
	*/

	// MODULES //

	var hasFloat64ArraySupport = requireMain$W();


	// EXPORTS //

	lib$14 = hasFloat64ArraySupport;
	return lib$14;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$V;
var hasRequiredMain$V;

function requireMain$V () {
	if (hasRequiredMain$V) return main$V;
	hasRequiredMain$V = 1;

	// MAIN //

	var ctor = ( typeof Float64Array === 'function' ) ? Float64Array : void 0; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	main$V = ctor;
	return main$V;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyfill_1$2;
var hasRequiredPolyfill$2;

function requirePolyfill$2 () {
	if (hasRequiredPolyfill$2) return polyfill_1$2;
	hasRequiredPolyfill$2 = 1;

	// TODO: write polyfill

	// MAIN //

	/**
	* Typed array which represents an array of double-precision floating-point numbers in the platform byte order.
	*
	* @throws {Error} not implemented
	*/
	function polyfill() {
		throw new Error( 'not implemented' );
	}


	// EXPORTS //

	polyfill_1$2 = polyfill;
	return polyfill_1$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$13;
var hasRequiredLib$13;

function requireLib$13 () {
	if (hasRequiredLib$13) return lib$13;
	hasRequiredLib$13 = 1;

	/**
	* Typed array constructor which returns a typed array representing an array of double-precision floating-point numbers in the platform byte order.
	*
	* @module @stdlib/array-float64
	*
	* @example
	* var ctor = require( '@stdlib/array-float64' );
	*
	* var arr = new ctor( 10 );
	* // returns <Float64Array>
	*/

	// MODULES //

	var hasFloat64ArraySupport = requireLib$14();
	var builtin = requireMain$V();
	var polyfill = requirePolyfill$2();


	// MAIN //

	var ctor;
	if ( hasFloat64ArraySupport() ) {
		ctor = builtin;
	} else {
		ctor = polyfill;
	}


	// EXPORTS //

	lib$13 = ctor;
	return lib$13;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$U;
var hasRequiredMain$U;

function requireMain$U () {
	if (hasRequiredMain$U) return main$U;
	hasRequiredMain$U = 1;

	// MODULES //

	var nativeClass = requireLib$1a();


	// VARIABLES //

	var hasUint8Array = ( typeof Uint8Array === 'function' ); // eslint-disable-line stdlib/require-globals


	// MAIN //

	/**
	* Tests if a value is a Uint8Array.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is a Uint8Array
	*
	* @example
	* var bool = isUint8Array( new Uint8Array( 10 ) );
	* // returns true
	*
	* @example
	* var bool = isUint8Array( [] );
	* // returns false
	*/
	function isUint8Array( value ) {
		return (
			( hasUint8Array && value instanceof Uint8Array ) || // eslint-disable-line stdlib/require-globals
			nativeClass( value ) === '[object Uint8Array]'
		);
	}


	// EXPORTS //

	main$U = isUint8Array;
	return main$U;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$12;
var hasRequiredLib$12;

function requireLib$12 () {
	if (hasRequiredLib$12) return lib$12;
	hasRequiredLib$12 = 1;

	/**
	* Test if a value is a Uint8Array.
	*
	* @module @stdlib/assert-is-uint8array
	*
	* @example
	* var isUint8Array = require( '@stdlib/assert-is-uint8array' );
	*
	* var bool = isUint8Array( new Uint8Array( 10 ) );
	* // returns true
	*
	* bool = isUint8Array( [] );
	* // returns false
	*/

	// MODULES //

	var isUint8Array = requireMain$U();


	// EXPORTS //

	lib$12 = isUint8Array;
	return lib$12;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$11;
var hasRequiredLib$11;

function requireLib$11 () {
	if (hasRequiredLib$11) return lib$11;
	hasRequiredLib$11 = 1;

	/**
	* Maximum unsigned 8-bit integer.
	*
	* @module @stdlib/constants-uint8-max
	* @type {integer32}
	*
	* @example
	* var UINT8_MAX = require( '@stdlib/constants-uint8-max' );
	* // returns 255
	*/


	// MAIN //

	/**
	* Maximum unsigned 8-bit integer.
	*
	* ## Notes
	*
	* The number has the value
	*
	* ```tex
	* 2^{8} - 1
	* ```
	*
	* which corresponds to the bit sequence
	*
	* ```binarystring
	* 11111111
	* ```
	*
	* @constant
	* @type {integer32}
	* @default 255
	*/
	var UINT8_MAX = 255|0; // asm type annotation


	// EXPORTS //

	lib$11 = UINT8_MAX;
	return lib$11;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var uint8array;
var hasRequiredUint8array;

function requireUint8array () {
	if (hasRequiredUint8array) return uint8array;
	hasRequiredUint8array = 1;

	// MAIN //

	var main = ( typeof Uint8Array === 'function' ) ? Uint8Array : null; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	uint8array = main;
	return uint8array;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$T;
var hasRequiredMain$T;

function requireMain$T () {
	if (hasRequiredMain$T) return main$T;
	hasRequiredMain$T = 1;

	// MODULES //

	var isUint8Array = requireLib$12();
	var UINT8_MAX = requireLib$11();
	var GlobalUint8Array = requireUint8array();


	// MAIN //

	/**
	* Tests for native `Uint8Array` support.
	*
	* @returns {boolean} boolean indicating if an environment has `Uint8Array` support
	*
	* @example
	* var bool = hasUint8ArraySupport();
	* // returns <boolean>
	*/
	function hasUint8ArraySupport() {
		var bool;
		var arr;

		if ( typeof GlobalUint8Array !== 'function' ) {
			return false;
		}
		// Test basic support...
		try {
			arr = [ 1, 3.14, -3.14, UINT8_MAX+1, UINT8_MAX+2 ];
			arr = new GlobalUint8Array( arr );
			bool = (
				isUint8Array( arr ) &&
				arr[ 0 ] === 1 &&
				arr[ 1 ] === 3 &&           // truncation
				arr[ 2 ] === UINT8_MAX-2 && // truncation and wrap around
				arr[ 3 ] === 0 &&           // wrap around
				arr[ 4 ] === 1              // wrap around
			);
		} catch ( err ) { // eslint-disable-line no-unused-vars
			bool = false;
		}
		return bool;
	}


	// EXPORTS //

	main$T = hasUint8ArraySupport;
	return main$T;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$10;
var hasRequiredLib$10;

function requireLib$10 () {
	if (hasRequiredLib$10) return lib$10;
	hasRequiredLib$10 = 1;

	/**
	* Test for native `Uint8Array` support.
	*
	* @module @stdlib/assert-has-uint8array-support
	*
	* @example
	* var hasUint8ArraySupport = require( '@stdlib/assert-has-uint8array-support' );
	*
	* var bool = hasUint8ArraySupport();
	* // returns <boolean>
	*/

	// MODULES //

	var hasUint8ArraySupport = requireMain$T();


	// EXPORTS //

	lib$10 = hasUint8ArraySupport;
	return lib$10;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$S;
var hasRequiredMain$S;

function requireMain$S () {
	if (hasRequiredMain$S) return main$S;
	hasRequiredMain$S = 1;

	// MAIN //

	var ctor = ( typeof Uint8Array === 'function' ) ? Uint8Array : void 0; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	main$S = ctor;
	return main$S;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyfill_1$1;
var hasRequiredPolyfill$1;

function requirePolyfill$1 () {
	if (hasRequiredPolyfill$1) return polyfill_1$1;
	hasRequiredPolyfill$1 = 1;

	// TODO: write polyfill

	// MAIN //

	/**
	* Typed array which represents an array of 8-bit unsigned integers in the platform byte order.
	*
	* @throws {Error} not implemented
	*/
	function polyfill() {
		throw new Error( 'not implemented' );
	}


	// EXPORTS //

	polyfill_1$1 = polyfill;
	return polyfill_1$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$$;
var hasRequiredLib$$;

function requireLib$$ () {
	if (hasRequiredLib$$) return lib$$;
	hasRequiredLib$$ = 1;

	/**
	* Typed array constructor which returns a typed array representing an array of 8-bit unsigned integers in the platform byte order.
	*
	* @module @stdlib/array-uint8
	*
	* @example
	* var ctor = require( '@stdlib/array-uint8' );
	*
	* var arr = new ctor( 10 );
	* // returns <Uint8Array>
	*/

	// MODULES //

	var hasUint8ArraySupport = requireLib$10();
	var builtin = requireMain$S();
	var polyfill = requirePolyfill$1();


	// MAIN //

	var ctor;
	if ( hasUint8ArraySupport() ) {
		ctor = builtin;
	} else {
		ctor = polyfill;
	}


	// EXPORTS //

	lib$$ = ctor;
	return lib$$;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$R;
var hasRequiredMain$R;

function requireMain$R () {
	if (hasRequiredMain$R) return main$R;
	hasRequiredMain$R = 1;

	// MODULES //

	var nativeClass = requireLib$1a();


	// VARIABLES //

	var hasUint16Array = ( typeof Uint16Array === 'function' ); // eslint-disable-line stdlib/require-globals


	// MAIN //

	/**
	* Tests if a value is a Uint16Array.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is a Uint16Array
	*
	* @example
	* var bool = isUint16Array( new Uint16Array( 10 ) );
	* // returns true
	*
	* @example
	* var bool = isUint16Array( [] );
	* // returns false
	*/
	function isUint16Array( value ) {
		return (
			( hasUint16Array && value instanceof Uint16Array ) || // eslint-disable-line stdlib/require-globals
			nativeClass( value ) === '[object Uint16Array]'
		);
	}


	// EXPORTS //

	main$R = isUint16Array;
	return main$R;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$_;
var hasRequiredLib$_;

function requireLib$_ () {
	if (hasRequiredLib$_) return lib$_;
	hasRequiredLib$_ = 1;

	/**
	* Test if a value is a Uint16Array.
	*
	* @module @stdlib/assert-is-uint16array
	*
	* @example
	* var isUint16Array = require( '@stdlib/assert-is-uint16array' );
	*
	* var bool = isUint16Array( new Uint16Array( 10 ) );
	* // returns true
	*
	* bool = isUint16Array( [] );
	* // returns false
	*/

	// MODULES //

	var isUint16Array = requireMain$R();


	// EXPORTS //

	lib$_ = isUint16Array;
	return lib$_;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$Z;
var hasRequiredLib$Z;

function requireLib$Z () {
	if (hasRequiredLib$Z) return lib$Z;
	hasRequiredLib$Z = 1;

	/**
	* Maximum unsigned 16-bit integer.
	*
	* @module @stdlib/constants-uint16-max
	* @type {integer32}
	*
	* @example
	* var UINT16_MAX = require( '@stdlib/constants-uint16-max' );
	* // returns 65535
	*/


	// MAIN //

	/**
	* Maximum unsigned 16-bit integer.
	*
	* ## Notes
	*
	* The number has the value
	*
	* ```tex
	* 2^{16} - 1
	* ```
	*
	* which corresponds to the bit sequence
	*
	* ```binarystring
	* 1111111111111111
	* ```
	*
	* @constant
	* @type {integer32}
	* @default 65535
	*/
	var UINT16_MAX = 65535|0; // asm type annotation


	// EXPORTS //

	lib$Z = UINT16_MAX;
	return lib$Z;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var uint16array;
var hasRequiredUint16array;

function requireUint16array () {
	if (hasRequiredUint16array) return uint16array;
	hasRequiredUint16array = 1;

	// MAIN //

	var main = ( typeof Uint16Array === 'function' ) ? Uint16Array : null; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	uint16array = main;
	return uint16array;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$Q;
var hasRequiredMain$Q;

function requireMain$Q () {
	if (hasRequiredMain$Q) return main$Q;
	hasRequiredMain$Q = 1;

	// MODULES //

	var isUint16Array = requireLib$_();
	var UINT16_MAX = requireLib$Z();
	var GlobalUint16Array = requireUint16array();


	// MAIN //

	/**
	* Tests for native `Uint16Array` support.
	*
	* @returns {boolean} boolean indicating if an environment has `Uint16Array` support
	*
	* @example
	* var bool = hasUint16ArraySupport();
	* // returns <boolean>
	*/
	function hasUint16ArraySupport() {
		var bool;
		var arr;

		if ( typeof GlobalUint16Array !== 'function' ) {
			return false;
		}
		// Test basic support...
		try {
			arr = [ 1, 3.14, -3.14, UINT16_MAX+1, UINT16_MAX+2 ];
			arr = new GlobalUint16Array( arr );
			bool = (
				isUint16Array( arr ) &&
				arr[ 0 ] === 1 &&
				arr[ 1 ] === 3 &&            // truncation
				arr[ 2 ] === UINT16_MAX-2 && // truncation and wrap around
				arr[ 3 ] === 0 &&            // wrap around
				arr[ 4 ] === 1               // wrap around
			);
		} catch ( err ) { // eslint-disable-line no-unused-vars
			bool = false;
		}
		return bool;
	}


	// EXPORTS //

	main$Q = hasUint16ArraySupport;
	return main$Q;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$Y;
var hasRequiredLib$Y;

function requireLib$Y () {
	if (hasRequiredLib$Y) return lib$Y;
	hasRequiredLib$Y = 1;

	/**
	* Test for native `Uint16Array` support.
	*
	* @module @stdlib/assert-has-uint16array-support
	*
	* @example
	* var hasUint16ArraySupport = require( '@stdlib/assert-has-uint16array-support' );
	*
	* var bool = hasUint16ArraySupport();
	* // returns <boolean>
	*/

	// MODULES //

	var hasUint16ArraySupport = requireMain$Q();


	// EXPORTS //

	lib$Y = hasUint16ArraySupport;
	return lib$Y;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$P;
var hasRequiredMain$P;

function requireMain$P () {
	if (hasRequiredMain$P) return main$P;
	hasRequiredMain$P = 1;

	// MAIN //

	var ctor = ( typeof Uint16Array === 'function' ) ? Uint16Array : void 0; // eslint-disable-line stdlib/require-globals


	// EXPORTS //

	main$P = ctor;
	return main$P;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyfill_1;
var hasRequiredPolyfill;

function requirePolyfill () {
	if (hasRequiredPolyfill) return polyfill_1;
	hasRequiredPolyfill = 1;

	// TODO: write polyfill

	// MAIN //

	/**
	* Typed array which represents an array of 16-bit unsigned integers in the platform byte order.
	*
	* @throws {Error} not implemented
	*/
	function polyfill() {
		throw new Error( 'not implemented' );
	}


	// EXPORTS //

	polyfill_1 = polyfill;
	return polyfill_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$X;
var hasRequiredLib$X;

function requireLib$X () {
	if (hasRequiredLib$X) return lib$X;
	hasRequiredLib$X = 1;

	/**
	* Typed array constructor which returns a typed array representing an array of 16-bit unsigned integers in the platform byte order.
	*
	* @module @stdlib/array-uint16
	*
	* @example
	* var ctor = require( '@stdlib/array-uint16' );
	*
	* var arr = new ctor( 10 );
	* // returns <Uint16Array>
	*/

	// MODULES //

	var hasUint16ArraySupport = requireLib$Y();
	var builtin = requireMain$P();
	var polyfill = requirePolyfill();


	// MAIN //

	var ctor;
	if ( hasUint16ArraySupport() ) {
		ctor = builtin;
	} else {
		ctor = polyfill;
	}


	// EXPORTS //

	lib$X = ctor;
	return lib$X;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var ctors_1;
var hasRequiredCtors;

function requireCtors () {
	if (hasRequiredCtors) return ctors_1;
	hasRequiredCtors = 1;

	// MODULES //

	var Uint8Array = requireLib$$();
	var Uint16Array = requireLib$X();


	// MAIN //

	var ctors = {
		'uint16': Uint16Array,
		'uint8': Uint8Array
	};


	// EXPORTS //

	ctors_1 = ctors;
	return ctors_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$O;
var hasRequiredMain$O;

function requireMain$O () {
	if (hasRequiredMain$O) return main$O;
	hasRequiredMain$O = 1;

	// MODULES //

	var ctors = requireCtors();


	// VARIABLES //

	var bool;


	// FUNCTIONS //

	/**
	* Returns a boolean indicating if an environment is little endian.
	*
	* @private
	* @returns {boolean} boolean indicating if an environment is little endian
	*
	* @example
	* var bool = isLittleEndian();
	* // returns <boolean>
	*/
	function isLittleEndian() {
		var uint16view;
		var uint8view;

		uint16view = new ctors[ 'uint16' ]( 1 );

		/*
		* Set the uint16 view to a value having distinguishable lower and higher order words.
		*
		* 4660 => 0x1234 => 0x12 0x34 => '00010010 00110100' => (0x12,0x34) == (18,52)
		*/
		uint16view[ 0 ] = 0x1234;

		// Create a uint8 view on top of the uint16 buffer:
		uint8view = new ctors[ 'uint8' ]( uint16view.buffer );

		// If little endian, the least significant byte will be first...
		return ( uint8view[ 0 ] === 0x34 );
	}


	// MAIN //

	bool = isLittleEndian();


	// EXPORTS //

	main$O = bool;
	return main$O;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$W;
var hasRequiredLib$W;

function requireLib$W () {
	if (hasRequiredLib$W) return lib$W;
	hasRequiredLib$W = 1;

	/**
	* Return a boolean indicating if an environment is little endian.
	*
	* @module @stdlib/assert-is-little-endian
	*
	* @example
	* var IS_LITTLE_ENDIAN = require( '@stdlib/assert-is-little-endian' );
	*
	* var bool = IS_LITTLE_ENDIAN;
	* // returns <boolean>
	*/

	// MODULES //

	var IS_LITTLE_ENDIAN = requireMain$O();


	// EXPORTS //

	lib$W = IS_LITTLE_ENDIAN;
	return lib$W;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var indices_1$1;
var hasRequiredIndices$1;

function requireIndices$1 () {
	if (hasRequiredIndices$1) return indices_1$1;
	hasRequiredIndices$1 = 1;

	// MODULES //

	var isLittleEndian = requireLib$W();


	// MAIN //

	var indices;
	var HIGH;
	var LOW;

	if ( isLittleEndian === true ) {
		HIGH = 1; // second index
		LOW = 0; // first index
	} else {
		HIGH = 0; // first index
		LOW = 1; // second index
	}
	indices = {
		'HIGH': HIGH,
		'LOW': LOW
	};


	// EXPORTS //

	indices_1$1 = indices;
	return indices_1$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var assign$1;
var hasRequiredAssign$1;

function requireAssign$1 () {
	if (hasRequiredAssign$1) return assign$1;
	hasRequiredAssign$1 = 1;

	// MODULES //

	var Uint32Array = requireLib$16();
	var Float64Array = requireLib$13();
	var indices = requireIndices$1();


	// VARIABLES //

	var FLOAT64_VIEW = new Float64Array( 1 );
	var UINT32_VIEW = new Uint32Array( FLOAT64_VIEW.buffer );

	var HIGH = indices.HIGH;
	var LOW = indices.LOW;


	// MAIN //

	/**
	* Splits a double-precision floating-point number into a higher order word (unsigned 32-bit integer) and a lower order word (unsigned 32-bit integer).
	*
	* ## Notes
	*
	* ```text
	* float64 (64 bits)
	* f := fraction (significand/mantissa) (52 bits)
	* e := exponent (11 bits)
	* s := sign bit (1 bit)
	*
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |                                Float64                                |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |              Uint32               |               Uint32              |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* ```
	*
	* If little endian (more significant bits last):
	*
	* ```text
	*                         <-- lower      higher -->
	* |   f7       f6       f5       f4       f3       f2    e2 | f1 |s|  e1  |
	* ```
	*
	* If big endian (more significant bits first):
	*
	* ```text
	*                         <-- higher      lower -->
	* |s| e1    e2 | f1     f2       f3       f4       f5        f6      f7   |
	* ```
	*
	* In which Uint32 can we find the higher order bits? If little endian, the second; if big endian, the first.
	*
	* ## References
	*
	* -   [Open Group][1]
	*
	* [1]: http://pubs.opengroup.org/onlinepubs/9629399/chap14.htm
	*
	* @private
	* @param {number} x - input value
	* @param {Collection} out - output array
	* @param {integer} stride - output array stride
	* @param {NonNegativeInteger} offset - output array index offset
	* @returns {Collection} output array
	*
	* @example
	* var Uint32Array = require( '@stdlib/array-uint32' );
	*
	* var out = new Uint32Array( 2 );
	*
	* var w = toWords( 3.14e201, out, 1, 0 );
	* // returns <Uint32Array>[ 1774486211, 2479577218 ]
	*
	* var bool = ( w === out );
	* // returns true
	*/
	function toWords( x, out, stride, offset ) {
		FLOAT64_VIEW[ 0 ] = x;
		out[ offset ] = UINT32_VIEW[ HIGH ];
		out[ offset + stride ] = UINT32_VIEW[ LOW ];
		return out;
	}


	// EXPORTS //

	assign$1 = toWords;
	return assign$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$N;
var hasRequiredMain$N;

function requireMain$N () {
	if (hasRequiredMain$N) return main$N;
	hasRequiredMain$N = 1;

	// MODULES //

	var fcn = requireAssign$1();


	// MAIN //

	/**
	* Splits a double-precision floating-point number into a higher order word (unsigned 32-bit integer) and a lower order word (unsigned 32-bit integer).
	*
	* @param {number} x - input value
	* @returns {Array<number>} output array
	*
	* @example
	* var w = toWords( 3.14e201 );
	* // returns [ 1774486211, 2479577218 ]
	*/
	function toWords( x ) {
		return fcn( x, [ 0>>>0, 0>>>0 ], 1, 0 );
	}


	// EXPORTS //

	main$N = toWords;
	return main$N;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$V;
var hasRequiredLib$V;

function requireLib$V () {
	if (hasRequiredLib$V) return lib$V;
	hasRequiredLib$V = 1;

	/**
	* Split a double-precision floating-point number into a higher order word (unsigned 32-bit integer) and a lower order word (unsigned 32-bit integer).
	*
	* @module @stdlib/number-float64-base-to-words
	*
	* @example
	* var toWords = require( '@stdlib/number-float64-base-to-words' );
	*
	* var w = toWords( 3.14e201 );
	* // returns [ 1774486211, 2479577218 ]
	*
	* @example
	* var Uint32Array = require( '@stdlib/array-uint32' );
	* var toWords = require( '@stdlib/number-float64-base-to-words' );
	*
	* var out = new Uint32Array( 2 );
	*
	* var w = toWords.assign( 3.14e201, out, 1, 0 );
	* // returns <Uint32Array>[ 1774486211, 2479577218 ]
	*
	* var bool = ( w === out );
	* // returns true
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$N();
	var assign = requireAssign$1();


	// MAIN //

	setReadOnly( main, 'assign', assign );


	// EXPORTS //

	lib$V = main;
	return lib$V;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var high$1;
var hasRequiredHigh$1;

function requireHigh$1 () {
	if (hasRequiredHigh$1) return high$1;
	hasRequiredHigh$1 = 1;

	// MODULES //

	var isLittleEndian = requireLib$W();


	// MAIN //

	var HIGH;
	if ( isLittleEndian === true ) {
		HIGH = 1; // second index
	} else {
		HIGH = 0; // first index
	}


	// EXPORTS //

	high$1 = HIGH;
	return high$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$M;
var hasRequiredMain$M;

function requireMain$M () {
	if (hasRequiredMain$M) return main$M;
	hasRequiredMain$M = 1;

	// MODULES //

	var Uint32Array = requireLib$16();
	var Float64Array = requireLib$13();
	var HIGH = requireHigh$1();


	// VARIABLES //

	var FLOAT64_VIEW = new Float64Array( 1 );
	var UINT32_VIEW = new Uint32Array( FLOAT64_VIEW.buffer );


	// MAIN //

	/**
	* Returns an unsigned 32-bit integer corresponding to the more significant 32 bits of a double-precision floating-point number.
	*
	* ## Notes
	*
	* ```text
	* float64 (64 bits)
	* f := fraction (significand/mantissa) (52 bits)
	* e := exponent (11 bits)
	* s := sign bit (1 bit)
	*
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |                                Float64                                |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |              Uint32               |               Uint32              |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* ```
	*
	* If little endian (more significant bits last):
	*
	* ```text
	*                         <-- lower      higher -->
	* |   f7       f6       f5       f4       f3       f2    e2 | f1 |s|  e1  |
	* ```
	*
	* If big endian (more significant bits first):
	*
	* ```text
	*                         <-- higher      lower -->
	* |s| e1    e2 | f1     f2       f3       f4       f5        f6      f7   |
	* ```
	*
	* In which Uint32 can we find the higher order bits? If little endian, the second; if big endian, the first.
	*
	* ## References
	*
	* -   [Open Group][1]
	*
	* [1]: http://pubs.opengroup.org/onlinepubs/9629399/chap14.htm
	*
	* @param {number} x - input value
	* @returns {uinteger32} higher order word
	*
	* @example
	* var w = getHighWord( 3.14e201 ); // => 01101001110001001000001011000011
	* // returns 1774486211
	*/
	function getHighWord( x ) {
		FLOAT64_VIEW[ 0 ] = x;
		return UINT32_VIEW[ HIGH ];
	}


	// EXPORTS //

	main$M = getHighWord;
	return main$M;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$U;
var hasRequiredLib$U;

function requireLib$U () {
	if (hasRequiredLib$U) return lib$U;
	hasRequiredLib$U = 1;

	/**
	* Return an unsigned 32-bit integer corresponding to the more significant 32 bits of a double-precision floating-point number.
	*
	* @module @stdlib/number-float64-base-get-high-word
	*
	* @example
	* var getHighWord = require( '@stdlib/number-float64-base-get-high-word' );
	*
	* var w = getHighWord( 3.14e201 ); // => 01101001110001001000001011000011
	* // returns 1774486211
	*/

	// MODULES //

	var main = requireMain$M();


	// EXPORTS //

	lib$U = main;
	return lib$U;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var indices_1;
var hasRequiredIndices;

function requireIndices () {
	if (hasRequiredIndices) return indices_1;
	hasRequiredIndices = 1;

	// MODULES //

	var isLittleEndian = requireLib$W();


	// MAIN //

	var indices;
	var HIGH;
	var LOW;

	if ( isLittleEndian === true ) {
		HIGH = 1; // second index
		LOW = 0; // first index
	} else {
		HIGH = 0; // first index
		LOW = 1; // second index
	}
	indices = {
		'HIGH': HIGH,
		'LOW': LOW
	};


	// EXPORTS //

	indices_1 = indices;
	return indices_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$L;
var hasRequiredMain$L;

function requireMain$L () {
	if (hasRequiredMain$L) return main$L;
	hasRequiredMain$L = 1;

	// MODULES //

	var Uint32Array = requireLib$16();
	var Float64Array = requireLib$13();
	var indices = requireIndices();


	// VARIABLES //

	var FLOAT64_VIEW = new Float64Array( 1 );
	var UINT32_VIEW = new Uint32Array( FLOAT64_VIEW.buffer );

	var HIGH = indices.HIGH;
	var LOW = indices.LOW;


	// MAIN //

	/**
	* Creates a double-precision floating-point number from a higher order word (unsigned 32-bit integer) and a lower order word (unsigned 32-bit integer).
	*
	* ## Notes
	*
	* ```text
	* float64 (64 bits)
	* f := fraction (significand/mantissa) (52 bits)
	* e := exponent (11 bits)
	* s := sign bit (1 bit)
	*
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |                                Float64                                |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |              Uint32               |               Uint32              |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* ```
	*
	* If little endian (more significant bits last):
	*
	* ```text
	*                         <-- lower      higher -->
	* |   f7       f6       f5       f4       f3       f2    e2 | f1 |s|  e1  |
	* ```
	*
	* If big endian (more significant bits first):
	*
	* ```text
	*                         <-- higher      lower -->
	* |s| e1    e2 | f1     f2       f3       f4       f5        f6      f7   |
	* ```
	*
	* In which Uint32 should we place the higher order bits? If little endian, the second; if big endian, the first.
	*
	* ## References
	*
	* -   [Open Group][1]
	*
	* [1]: http://pubs.opengroup.org/onlinepubs/9629399/chap14.htm
	*
	* @param {uinteger32} high - higher order word (unsigned 32-bit integer)
	* @param {uinteger32} low - lower order word (unsigned 32-bit integer)
	* @returns {number} floating-point number
	*
	* @example
	* var v = fromWords( 1774486211, 2479577218 );
	* // returns 3.14e201
	*
	* @example
	* var v = fromWords( 3221823995, 1413754136 );
	* // returns -3.141592653589793
	*
	* @example
	* var v = fromWords( 0, 0 );
	* // returns 0.0
	*
	* @example
	* var v = fromWords( 2147483648, 0 );
	* // returns -0.0
	*
	* @example
	* var v = fromWords( 2146959360, 0 );
	* // returns NaN
	*
	* @example
	* var v = fromWords( 2146435072, 0 );
	* // returns Infinity
	*
	* @example
	* var v = fromWords( 4293918720, 0 );
	* // returns -Infinity
	*/
	function fromWords( high, low ) {
		UINT32_VIEW[ HIGH ] = high;
		UINT32_VIEW[ LOW ] = low;
		return FLOAT64_VIEW[ 0 ];
	}


	// EXPORTS //

	main$L = fromWords;
	return main$L;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$T;
var hasRequiredLib$T;

function requireLib$T () {
	if (hasRequiredLib$T) return lib$T;
	hasRequiredLib$T = 1;

	/**
	* Create a double-precision floating-point number from a higher order word (unsigned 32-bit integer) and a lower order word (unsigned 32-bit integer).
	*
	* @module @stdlib/number-float64-base-from-words
	*
	* @example
	* var fromWords = require( '@stdlib/number-float64-base-from-words' );
	*
	* var v = fromWords( 1774486211, 2479577218 );
	* // returns 3.14e201
	*
	* v = fromWords( 3221823995, 1413754136 );
	* // returns -3.141592653589793
	*
	* v = fromWords( 0, 0 );
	* // returns 0.0
	*
	* v = fromWords( 2147483648, 0 );
	* // returns -0.0
	*
	* v = fromWords( 2146959360, 0 );
	* // returns NaN
	*
	* v = fromWords( 2146435072, 0 );
	* // returns Infinity
	*
	* v = fromWords( 4293918720, 0 );
	* // returns -Infinity
	*/

	// MODULES //

	var main = requireMain$L();


	// EXPORTS //

	lib$T = main;
	return lib$T;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$K;
var hasRequiredMain$K;

function requireMain$K () {
	if (hasRequiredMain$K) return main$K;
	hasRequiredMain$K = 1;

	// MODULES //

	var SIGN_MASK = requireLib$1g();
	var ABS_MASK = requireLib$1f();
	var toWords = requireLib$V();
	var getHighWord = requireLib$U();
	var fromWords = requireLib$T();


	// VARIABLES //

	// High/low words workspace:
	var WORDS = [ 0, 0 ];


	// MAIN //

	/**
	* Returns a double-precision floating-point number with the magnitude of `x` and the sign of `y`.
	*
	* @param {number} x - number from which to derive a magnitude
	* @param {number} y - number from which to derive a sign
	* @returns {number} a double-precision floating-point number
	*
	* @example
	* var z = copysign( -3.14, 10.0 );
	* // returns 3.14
	*
	* @example
	* var z = copysign( 3.14, -1.0 );
	* // returns -3.14
	*
	* @example
	* var z = copysign( 1.0, -0.0 );
	* // returns -1.0
	*
	* @example
	* var z = copysign( -3.14, -0.0 );
	* // returns -3.14
	*
	* @example
	* var z = copysign( -0.0, 1.0 );
	* // returns 0.0
	*/
	function copysign( x, y ) {
		var hx;
		var hy;

		// Split `x` into higher and lower order words:
		toWords.assign( x, WORDS, 1, 0 );
		hx = WORDS[ 0 ];

		// Turn off the sign bit of `x`:
		hx &= ABS_MASK;

		// Extract the higher order word from `y`:
		hy = getHighWord( y );

		// Leave only the sign bit of `y` turned on:
		hy &= SIGN_MASK;

		// Copy the sign bit of `y` to `x`:
		hx |= hy;

		// Return a new value having the same magnitude as `x`, but with the sign of `y`:
		return fromWords( hx, WORDS[ 1 ] );
	}


	// EXPORTS //

	main$K = copysign;
	return main$K;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$S;
var hasRequiredLib$S;

function requireLib$S () {
	if (hasRequiredLib$S) return lib$S;
	hasRequiredLib$S = 1;

	/**
	* Return a double-precision floating-point number with the magnitude of `x` and the sign of `y`.
	*
	* @module @stdlib/math-base-special-copysign
	*
	* @example
	* var copysign = require( '@stdlib/math-base-special-copysign' );
	*
	* var z = copysign( -3.14, 10.0 );
	* // returns 3.14
	*
	* z = copysign( 3.14, -1.0 );
	* // returns -3.14
	*
	* z = copysign( 1.0, -0.0 );
	* // returns -1.0
	*
	* z = copysign( -3.14, -0.0 );
	* // returns -3.14
	*
	* z = copysign( -0.0, 1.0 );
	* // returns 0.0
	*/

	// MODULES //

	var main = requireMain$K();


	// EXPORTS //

	lib$S = main;
	return lib$S;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$R;
var hasRequiredLib$R;

function requireLib$R () {
	if (hasRequiredLib$R) return lib$R;
	hasRequiredLib$R = 1;

	/**
	* Smallest positive double-precision floating-point normal number.
	*
	* @module @stdlib/constants-float64-smallest-normal
	* @type {number}
	*
	* @example
	* var FLOAT64_SMALLEST_NORMAL = require( '@stdlib/constants-float64-smallest-normal' );
	* // returns 2.2250738585072014e-308
	*/


	// MAIN //

	/**
	* The smallest positive double-precision floating-point normal number.
	*
	* ## Notes
	*
	* The number has the value
	*
	* ```tex
	* \frac{1}{2^{1023-1}}
	* ```
	*
	* which corresponds to the bit sequence
	*
	* ```binarystring
	* 0 00000000001 00000000000000000000 00000000000000000000000000000000
	* ```
	*
	* @constant
	* @type {number}
	* @default 2.2250738585072014e-308
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_SMALLEST_NORMAL = 2.2250738585072014e-308;


	// EXPORTS //

	lib$R = FLOAT64_SMALLEST_NORMAL;
	return lib$R;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2021 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$J;
var hasRequiredMain$J;

function requireMain$J () {
	if (hasRequiredMain$J) return main$J;
	hasRequiredMain$J = 1;

	// MAIN //

	/**
	* Computes the absolute value of a double-precision floating-point number `x`.
	*
	* @param {number} x - input value
	* @returns {number} absolute value
	*
	* @example
	* var v = abs( -1.0 );
	* // returns 1.0
	*
	* @example
	* var v = abs( 2.0 );
	* // returns 2.0
	*
	* @example
	* var v = abs( 0.0 );
	* // returns 0.0
	*
	* @example
	* var v = abs( -0.0 );
	* // returns 0.0
	*
	* @example
	* var v = abs( NaN );
	* // returns NaN
	*/
	function abs( x ) {
		return Math.abs( x ); // eslint-disable-line stdlib/no-builtin-math
	}


	// EXPORTS //

	main$J = abs;
	return main$J;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$Q;
var hasRequiredLib$Q;

function requireLib$Q () {
	if (hasRequiredLib$Q) return lib$Q;
	hasRequiredLib$Q = 1;

	/**
	* Compute an absolute value of a double-precision floating-point number.
	*
	* @module @stdlib/math-base-special-abs
	*
	* @example
	* var abs = require( '@stdlib/math-base-special-abs' );
	*
	* var v = abs( -1.0 );
	* // returns 1.0
	*
	* v = abs( 2.0 );
	* // returns 2.0
	*
	* v = abs( 0.0 );
	* // returns 0.0
	*
	* v = abs( -0.0 );
	* // returns 0.0
	*
	* v = abs( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$J();


	// EXPORTS //

	lib$Q = main;
	return lib$Q;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var assign;
var hasRequiredAssign;

function requireAssign () {
	if (hasRequiredAssign) return assign;
	hasRequiredAssign = 1;

	// MODULES //

	var FLOAT64_SMALLEST_NORMAL = requireLib$R();
	var isInfinite = requireLib$1h();
	var isnan = requireLib$1s();
	var abs = requireLib$Q();


	// VARIABLES //

	// (1<<52)
	var SCALAR = 4503599627370496;


	// MAIN //

	/**
	* Returns a normal number `y` and exponent `exp` satisfying \\(x = y \cdot 2^\mathrm{exp}\\) and assigns results to a provided output array.
	*
	* @param {number} x - input value
	* @param {Collection} out - output array
	* @param {integer} stride - output array stride
	* @param {NonNegativeInteger} offset - output array index offset
	* @returns {Collection} output array
	*
	* @example
	* var pow = require( '@stdlib/math-base-special-pow' );
	*
	* var out = normalize( 3.14e-319, [ 0.0, 0 ], 1, 0 );
	* // returns [ 1.4141234400356668e-303, -52 ]
	*
	* var y = out[ 0 ];
	* var exp = out[ 1 ];
	*
	* var bool = ( y*pow(2.0,exp) === 3.14e-319 );
	* // returns true
	*
	* @example
	* var out = normalize( 0.0, [ 0.0, 0 ], 1, 0 );
	* // returns [ 0.0, 0 ];
	*
	* @example
	* var PINF = require( '@stdlib/constants-float64-pinf' );
	*
	* var out = normalize( PINF, [ 0.0, 0 ], 1, 0 );
	* // returns [ Infinity, 0 ]
	*
	* @example
	* var NINF = require( '@stdlib/constants-float64-ninf' );
	*
	* var out = normalize( NINF, [ 0.0, 0 ], 1, 0 );
	* // returns [ -Infinity, 0 ]
	*
	* @example
	* var out = normalize( NaN, [ 0.0, 0 ], 1, 0 );
	* // returns [ NaN, 0 ]
	*/
	function normalize( x, out, stride, offset ) {
		if ( isnan( x ) || isInfinite( x ) ) {
			out[ offset ] = x;
			out[ offset + stride ] = 0;
			return out;
		}
		if ( x !== 0.0 && abs( x ) < FLOAT64_SMALLEST_NORMAL ) {
			out[ offset ] = x * SCALAR;
			out[ offset + stride ] = -52;
			return out;
		}
		out[ offset ] = x;
		out[ offset + stride ] = 0;
		return out;
	}


	// EXPORTS //

	assign = normalize;
	return assign;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$I;
var hasRequiredMain$I;

function requireMain$I () {
	if (hasRequiredMain$I) return main$I;
	hasRequiredMain$I = 1;

	// MODULES //

	var fcn = requireAssign();


	// MAIN //

	/**
	* Returns a normal number `y` and exponent `exp` satisfying \\(x = y \cdot 2^\mathrm{exp}\\).
	*
	* @param {number} x - input value
	* @returns {NumberArray} output array
	*
	* @example
	* var pow = require( '@stdlib/math-base-special-pow' );
	*
	* var out = normalize( 3.14e-319 );
	* // returns [ 1.4141234400356668e-303, -52 ]
	*
	* var y = out[ 0 ];
	* var exp = out[ 1 ];
	*
	* var bool = ( y*pow(2.0,exp) === 3.14e-319 );
	* // returns true
	*
	* @example
	* var out = normalize( 0.0 );
	* // returns [ 0.0, 0 ]
	*
	* @example
	* var PINF = require( '@stdlib/constants-float64-pinf' );
	*
	* var out = normalize( PINF );
	* // returns [ Infinity, 0 ]
	*
	* @example
	* var NINF = require( '@stdlib/constants-float64-ninf' );
	*
	* var out = normalize( NINF );
	* // returns [ -Infinity, 0 ]
	*
	* @example
	* var out = normalize( NaN );
	* // returns [ NaN, 0 ]
	*/
	function normalize( x ) {
		return fcn( x, [ 0.0, 0 ], 1, 0 );
	}


	// EXPORTS //

	main$I = normalize;
	return main$I;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$P;
var hasRequiredLib$P;

function requireLib$P () {
	if (hasRequiredLib$P) return lib$P;
	hasRequiredLib$P = 1;

	/**
	* Return a normal number `y` and exponent `exp` satisfying \\(x = y \cdot 2^\mathrm{exp}\\).
	*
	* @module @stdlib/number-float64-base-normalize
	*
	* @example
	* var normalize = require( '@stdlib/number-float64-base-normalize' );
	* var pow = require( '@stdlib/math-base-special-pow' );
	*
	* var out = normalize( 3.14e-319 );
	* // returns [ 1.4141234400356668e-303, -52 ]
	*
	* var y = out[ 0 ];
	* var exp = out[ 1 ];
	*
	* var bool = ( y*pow(2.0, exp) === 3.14e-319 );
	* // returns true
	*
	* @example
	* var Float64Array = require( '@stdlib/array-float64' );
	* var normalize = require( '@stdlib/number-float64-base-normalize' );
	*
	* var out = new Float64Array( 2 );
	*
	* var v = normalize.assign( 3.14e-319, out, 1, 0 );
	* // returns <Float64Array>[ 1.4141234400356668e-303, -52 ]
	*
	* var bool = ( v === out );
	* // returns true
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$I();
	var assign = requireAssign();


	// MAIN //

	setReadOnly( main, 'assign', assign );


	// EXPORTS //

	lib$P = main;
	return lib$P;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$O;
var hasRequiredLib$O;

function requireLib$O () {
	if (hasRequiredLib$O) return lib$O;
	hasRequiredLib$O = 1;

	/**
	* High word mask for the exponent of a double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-high-word-exponent-mask
	* @type {uinteger32}
	*
	* @example
	* var FLOAT64_HIGH_WORD_EXPONENT_MASK = require( '@stdlib/constants-float64-high-word-exponent-mask' );
	* // returns 2146435072
	*/


	// MAIN //

	/**
	* High word mask for the exponent of a double-precision floating-point number.
	*
	* ## Notes
	*
	* The high word mask for the exponent of a double-precision floating-point number is an unsigned 32-bit integer with the value \\( 2146435072 \\), which corresponds to the bit sequence
	*
	* ```binarystring
	* 0 11111111111 00000000000000000000
	* ```
	*
	* @constant
	* @type {uinteger32}
	* @default 0x7ff00000
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_HIGH_WORD_EXPONENT_MASK = 0x7ff00000;


	// EXPORTS //

	lib$O = FLOAT64_HIGH_WORD_EXPONENT_MASK;
	return lib$O;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$H;
var hasRequiredMain$H;

function requireMain$H () {
	if (hasRequiredMain$H) return main$H;
	hasRequiredMain$H = 1;

	// MODULES //

	var getHighWord = requireLib$U();
	var EXP_MASK = requireLib$O();
	var BIAS = requireLib$1l();


	// MAIN //

	/**
	* Returns an integer corresponding to the unbiased exponent of a double-precision floating-point number.
	*
	* @param {number} x - input value
	* @returns {integer32} unbiased exponent
	*
	* @example
	* var exp = exponent( 3.14e-307 ); // => 2**-1019 ~ 1e-307
	* // returns -1019
	*
	* @example
	* var exp = exponent( -3.14 );
	* // returns 1
	*
	* @example
	* var exp = exponent( 0.0 );
	* // returns -1023
	*
	* @example
	* var exp = exponent( NaN );
	* // returns 1024
	*/
	function exponent( x ) {
		// Extract from the input value a higher order word (unsigned 32-bit integer) which contains the exponent:
		var high = getHighWord( x );

		// Apply a mask to isolate only the exponent bits and then shift off all bits which are part of the fraction:
		high = ( high & EXP_MASK ) >>> 20;

		// Remove the bias and return:
		return (high - BIAS)|0; // asm type annotation
	}


	// EXPORTS //

	main$H = exponent;
	return main$H;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$N;
var hasRequiredLib$N;

function requireLib$N () {
	if (hasRequiredLib$N) return lib$N;
	hasRequiredLib$N = 1;

	/**
	* Return an integer corresponding to the unbiased exponent of a double-precision floating-point number.
	*
	* @module @stdlib/number-float64-base-exponent
	*
	* @example
	* var exponent = require( '@stdlib/number-float64-base-exponent' );
	*
	* var exp = exponent( 3.14e-307 ); // => 2**-1019 ~ 1e-307
	* // returns -1019
	*
	* exp = exponent( -3.14 );
	* // returns 1
	*
	* exp = exponent( 0.0 );
	* // returns -1023
	*
	* exp = exponent( NaN );
	* // returns 1024
	*/

	// MODULES //

	var main = requireMain$H();


	// EXPORTS //

	lib$N = main;
	return lib$N;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$G;
var hasRequiredMain$G;

function requireMain$G () {
	if (hasRequiredMain$G) return main$G;
	hasRequiredMain$G = 1;

	// NOTES //

	/*
	* => ldexp: load exponent (see [The Open Group]{@link http://pubs.opengroup.org/onlinepubs/9699919799/functions/ldexp.html} and [cppreference]{@link http://en.cppreference.com/w/c/numeric/math/ldexp}).
	*/


	// MODULES //

	var PINF = requireLib$1m();
	var NINF = requireLib$1n();
	var BIAS = requireLib$1l();
	var MAX_EXPONENT = requireLib$1k();
	var MAX_SUBNORMAL_EXPONENT = requireLib$1j();
	var MIN_SUBNORMAL_EXPONENT = requireLib$1i();
	var isnan = requireLib$1s();
	var isInfinite = requireLib$1h();
	var copysign = requireLib$S();
	var normalize = requireLib$P().assign;
	var floatExp = requireLib$N();
	var toWords = requireLib$V();
	var fromWords = requireLib$T();


	// VARIABLES //

	// 1/(1<<52) = 1/(2**52) = 1/4503599627370496
	var TWO52_INV = 2.220446049250313e-16;

	// Exponent all 0s: 1 00000000000 11111111111111111111 => 2148532223
	var CLEAR_EXP_MASK = 0x800fffff>>>0; // asm type annotation

	// Normalization workspace:
	var FRAC = [ 0.0, 0.0 ];

	// High/low words workspace:
	var WORDS = [ 0, 0 ];


	// MAIN //

	/**
	* Multiplies a double-precision floating-point number by an integer power of two.
	*
	* @param {number} frac - fraction
	* @param {integer} exp - exponent
	* @returns {number} double-precision floating-point number
	*
	* @example
	* var x = ldexp( 0.5, 3 ); // => 0.5 * 2^3 = 0.5 * 8
	* // returns 4.0
	*
	* @example
	* var x = ldexp( 4.0, -2 ); // => 4 * 2^(-2) = 4 * (1/4)
	* // returns 1.0
	*
	* @example
	* var x = ldexp( 0.0, 20 );
	* // returns 0.0
	*
	* @example
	* var x = ldexp( -0.0, 39 );
	* // returns -0.0
	*
	* @example
	* var x = ldexp( NaN, -101 );
	* // returns NaN
	*
	* @example
	* var x = ldexp( Infinity, 11 );
	* // returns Infinity
	*
	* @example
	* var x = ldexp( -Infinity, -118 );
	* // returns -Infinity
	*/
	function ldexp( frac, exp ) {
		var high;
		var m;
		if (
			exp === 0 ||
			frac === 0.0 || // handles +-0
			isnan( frac ) ||
			isInfinite( frac )
		) {
			return frac;
		}
		// Normalize the input fraction:
		normalize( frac, FRAC, 1, 0 );
		frac = FRAC[ 0 ];
		exp += FRAC[ 1 ];

		// Extract the exponent from `frac` and add it to `exp`:
		exp += floatExp( frac );

		// Check for underflow/overflow...
		if ( exp < MIN_SUBNORMAL_EXPONENT ) {
			return copysign( 0.0, frac );
		}
		if ( exp > MAX_EXPONENT ) {
			if ( frac < 0.0 ) {
				return NINF;
			}
			return PINF;
		}
		// Check for a subnormal and scale accordingly to retain precision...
		if ( exp <= MAX_SUBNORMAL_EXPONENT ) {
			exp += 52;
			m = TWO52_INV;
		} else {
			m = 1.0;
		}
		// Split the fraction into higher and lower order words:
		toWords.assign( frac, WORDS, 1, 0 );
		high = WORDS[ 0 ];

		// Clear the exponent bits within the higher order word:
		high &= CLEAR_EXP_MASK;

		// Set the exponent bits to the new exponent:
		high |= ((exp+BIAS) << 20);

		// Create a new floating-point number:
		return m * fromWords( high, WORDS[ 1 ] );
	}


	// EXPORTS //

	main$G = ldexp;
	return main$G;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$M;
var hasRequiredLib$M;

function requireLib$M () {
	if (hasRequiredLib$M) return lib$M;
	hasRequiredLib$M = 1;

	/**
	* Multiply a double-precision floating-point number by an integer power of two.
	*
	* @module @stdlib/math-base-special-ldexp
	*
	* @example
	* var ldexp = require( '@stdlib/math-base-special-ldexp' );
	*
	* var x = ldexp( 0.5, 3 ); // => 0.5 * 2^3 = 0.5 * 8
	* // returns 4.0
	*
	* x = ldexp( 4.0, -2 ); // => 4 * 2^(-2) = 4 * (1/4)
	* // returns 1.0
	*
	* x = ldexp( 0.0, 20 );
	* // returns 0.0
	*
	* x = ldexp( -0.0, 39 );
	* // returns -0.0
	*
	* x = ldexp( NaN, -101 );
	* // returns NaN
	*
	* x = ldexp( Infinity, 11 );
	* // returns Infinity
	*
	* x = ldexp( -Infinity, -118 );
	* // returns -Infinity
	*/

	// MODULES //

	var main = requireMain$G();


	// EXPORTS //

	lib$M = main;
	return lib$M;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_p$3;
var hasRequiredPolyval_p$3;

function requirePolyval_p$3 () {
	if (hasRequiredPolyval_p$3) return polyval_p$3;
	hasRequiredPolyval_p$3 = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.16666666666666602;
		}
		return 0.16666666666666602 + (x * (-0.0027777777777015593 + (x * (0.00006613756321437934 + (x * (-0.0000016533902205465252 + (x * 4.1381367970572385e-8))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_p$3 = evalpoly;
	return polyval_p$3;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyrights, licenses, and long comment were part of the original implementation available as part of [Go]{@link https://github.com/golang/go/blob/cb07765045aed5104a3df31507564ac99e6ddce8/src/math/exp.go}, which in turn was based on an implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/e_exp.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (c) 2009 The Go Authors. All rights reserved.
*
* Redistribution and use in source and binary forms, with or without
* modification, are permitted provided that the following conditions are
* met:
*
*    * Redistributions of source code must retain the above copyright
* notice, this list of conditions and the following disclaimer.
*    * Redistributions in binary form must reproduce the above
* copyright notice, this list of conditions and the following disclaimer
* in the documentation and/or other materials provided with the
* distribution.
*    * Neither the name of Google Inc. nor the names of its
* contributors may be used to endorse or promote products derived from
* this software without specific prior written permission.
*
* THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
* "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
* LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
* A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
* OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
* SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
* LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
* DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
* THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
* (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
* OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
* ```
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var expmulti_1;
var hasRequiredExpmulti;

function requireExpmulti () {
	if (hasRequiredExpmulti) return expmulti_1;
	hasRequiredExpmulti = 1;

	// MODULES //

	var ldexp = requireLib$M();
	var polyvalP = requirePolyval_p$3();


	// MAIN //

	/**
	* Computes \\(e^{r} 2^k\\) where \\(r = \mathrm{hi} - \mathrm{lo}\\) and \\(|r| \leq \ln(2)/2\\).
	*
	* @private
	* @param {number} hi - upper bound
	* @param {number} lo - lower bound
	* @param {integer} k - power of 2
	* @returns {number} function value
	*/
	function expmulti( hi, lo, k ) {
		var r;
		var t;
		var c;
		var y;

		r = hi - lo;
		t = r * r;
		c = r - ( t*polyvalP( t ) );
		y = 1.0 - ( lo - ( (r*c)/(2.0-c) ) - hi );

		return ldexp( y, k );
	}


	// EXPORTS //

	expmulti_1 = expmulti;
	return expmulti_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyrights, licenses, and long comment were part of the original implementation available as part of [Go]{@link https://github.com/golang/go/blob/cb07765045aed5104a3df31507564ac99e6ddce8/src/math/exp.go}, which in turn was based on an implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/e_exp.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (c) 2009 The Go Authors. All rights reserved.
*
* Redistribution and use in source and binary forms, with or without
* modification, are permitted provided that the following conditions are
* met:
*
*    * Redistributions of source code must retain the above copyright
* notice, this list of conditions and the following disclaimer.
*    * Redistributions in binary form must reproduce the above
* copyright notice, this list of conditions and the following disclaimer
* in the documentation and/or other materials provided with the
* distribution.
*    * Neither the name of Google Inc. nor the names of its
* contributors may be used to endorse or promote products derived from
* this software without specific prior written permission.
*
* THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
* "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
* LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
* A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
* OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
* SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
* LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
* DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
* THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
* (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
* OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
* ```
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var main$F;
var hasRequiredMain$F;

function requireMain$F () {
	if (hasRequiredMain$F) return main$F;
	hasRequiredMain$F = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var trunc = requireLib$1p();
	var NINF = requireLib$1n();
	var PINF = requireLib$1m();
	var expmulti = requireExpmulti();


	// VARIABLES //

	var LN2_HI = 6.93147180369123816490e-01;
	var LN2_LO = 1.90821492927058770002e-10;
	var LOG2_E = 1.44269504088896338700e+00;
	var OVERFLOW = 7.09782712893383973096e+02;
	var UNDERFLOW = -7.45133219101941108420e+02;
	var NEARZERO = 1.0 / (1 << 28); // 2^-28
	var NEG_NEARZERO = -NEARZERO;


	// MAIN //

	/**
	* Evaluates the natural exponential function.
	*
	* ## Method
	*
	* 1.  We reduce \\( x \\) to an \\( r \\) so that \\( |r| \leq 0.5 \cdot \ln(2) \approx 0.34658 \\). Given \\( x \\), we find an \\( r \\) and integer \\( k \\) such that
	*
	*     ```tex
	*     \begin{align*}
	*     x &= k \cdot \ln(2) + r \\
	*     |r| &\leq 0.5 \cdot \ln(2)
	*     \end{align*}
	*     ```
	*
	*     <!-- <note> -->
	*
	*     \\( r \\) can be represented as \\( r = \mathrm{hi} - \mathrm{lo} \\) for better accuracy.
	*
	*     <!-- </note> -->
	*
	* 2.  We approximate of \\( e^{r} \\) by a special rational function on the interval \\(\[0,0.34658]\\):
	*
	*     ```tex
	*     \begin{align*}
	*     R\left(r^2\right) &= r \cdot \frac{ e^{r}+1 }{ e^{r}-1 } \\
	*     &= 2 + \frac{r^2}{6} - \frac{r^4}{360} + \ldots
	*     \end{align*}
	*     ```
	*
	*     We use a special Remes algorithm on \\(\[0,0.34658]\\) to generate a polynomial of degree \\(5\\) to approximate \\(R\\). The maximum error of this polynomial approximation is bounded by \\(2^{-59}\\). In other words,
	*
	*     ```tex
	*     R(z) \sim 2 + P_1 z + P_2 z^2 + P_3 z^3 + P_4 z^4 + P_5 z^5
	*     ```
	*
	*     where \\( z = r^2 \\) and
	*
	*     ```tex
	*     \left|  2 + P_1 z + \ldots + P_5 z^5  - R(z) \right| \leq 2^{-59}
	*     ```
	*
	*     <!-- <note> -->
	*
	*     The values of \\( P_1 \\) to \\( P_5 \\) are listed in the source code.
	*
	*     <!-- </note> -->
	*
	*     The computation of \\( e^{r} \\) thus becomes
	*
	*     ```tex
	*     \begin{align*}
	*     e^{r} &= 1 + \frac{2r}{R-r} \\
	*           &= 1 + r + \frac{r \cdot R_1(r)}{2 - R_1(r)}\ \text{for better accuracy}
	*     \end{align*}
	*     ```
	*
	*     where
	*
	*     ```tex
	*     R_1(r) = r - P_1\ r^2 + P_2\ r^4 + \ldots + P_5\ r^{10}
	*     ```
	*
	* 3.  We scale back to obtain \\( e^{x} \\). From step 1, we have
	*
	*     ```tex
	*     e^{x} = 2^k e^{r}
	*     ```
	*
	* ## Special Cases
	*
	* ```tex
	* \begin{align*}
	* e^\infty &= \infty \\
	* e^{-\infty} &= 0 \\
	* e^{\mathrm{NaN}} &= \mathrm{NaN} \\
	* e^0 &= 1\ \mathrm{is\ exact\ for\ finite\ argument\ only}
	* \end{align*}
	* ```
	*
	* ## Notes
	*
	* -   According to an error analysis, the error is always less than \\(1\\) ulp (unit in the last place).
	*
	* -   For an IEEE double,
	*
	*     -   if \\(x > 7.09782712893383973096\mbox{e+}02\\), then \\(e^{x}\\) overflows
	*     -   if \\(x < -7.45133219101941108420\mbox{e+}02\\), then \\(e^{x}\\) underflows
	*
	* -   The hexadecimal values included in the source code are the intended ones for the used constants. Decimal values may be used, provided that the compiler will convert from decimal to binary accurately enough to produce the intended hexadecimal values.
	*
	* @param {number} x - input value
	* @returns {number} function value
	*
	* @example
	* var v = exp( 4.0 );
	* // returns ~54.5982
	*
	* @example
	* var v = exp( -9.0 );
	* // returns ~1.234e-4
	*
	* @example
	* var v = exp( 0.0 );
	* // returns 1.0
	*
	* @example
	* var v = exp( NaN );
	* // returns NaN
	*/
	function exp( x ) {
		var hi;
		var lo;
		var k;

		if ( isnan( x ) || x === PINF ) {
			return x;
		}
		if ( x === NINF ) {
			return 0.0;
		}
		if ( x > OVERFLOW ) {
			return PINF;
		}
		if ( x < UNDERFLOW ) {
			return 0.0;
		}
		if (
			x > NEG_NEARZERO &&
			x < NEARZERO
		) {
			return 1.0 + x;
		}
		// Reduce and compute `r = hi - lo` for extra precision...
		if ( x < 0.0 ) {
			k = trunc( (LOG2_E*x) - 0.5 );
		} else {
			k = trunc( (LOG2_E*x) + 0.5 );
		}
		hi = x - (k*LN2_HI);
		lo = k * LN2_LO;

		return expmulti( hi, lo, k );
	}


	// EXPORTS //

	main$F = exp;
	return main$F;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$L;
var hasRequiredLib$L;

function requireLib$L () {
	if (hasRequiredLib$L) return lib$L;
	hasRequiredLib$L = 1;

	/**
	* Evaluate the natural exponential function.
	*
	* @module @stdlib/math-base-special-exp
	*
	* @example
	* var exp = require( '@stdlib/math-base-special-exp' );
	*
	* var v = exp( 4.0 );
	* // returns ~54.5982
	*
	* v = exp( -9.0 );
	* // returns ~1.234e-4
	*
	* v = exp( 0.0 );
	* // returns 1.0
	*
	* v = exp( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$F();


	// EXPORTS //

	lib$L = main;
	return lib$L;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var low;
var hasRequiredLow;

function requireLow () {
	if (hasRequiredLow) return low;
	hasRequiredLow = 1;

	// MODULES //

	var isLittleEndian = requireLib$W();


	// MAIN //

	var LOW;
	if ( isLittleEndian === true ) {
		LOW = 0; // first index
	} else {
		LOW = 1; // second index
	}


	// EXPORTS //

	low = LOW;
	return low;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$E;
var hasRequiredMain$E;

function requireMain$E () {
	if (hasRequiredMain$E) return main$E;
	hasRequiredMain$E = 1;

	// MODULES //

	var Uint32Array = requireLib$16();
	var Float64Array = requireLib$13();
	var LOW = requireLow();


	// VARIABLES //

	var FLOAT64_VIEW = new Float64Array( 1 );
	var UINT32_VIEW = new Uint32Array( FLOAT64_VIEW.buffer );


	// MAIN //

	/**
	* Sets the less significant 32 bits of a double-precision floating-point number.
	*
	* ## Notes
	*
	* ```text
	* float64 (64 bits)
	* f := fraction (significand/mantissa) (52 bits)
	* e := exponent (11 bits)
	* s := sign bit (1 bit)
	*
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |                                Float64                                |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |              Uint32               |               Uint32              |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* ```
	*
	* If little endian (more significant bits last):
	*
	* ```text
	*                         <-- lower      higher -->
	* |   f7       f6       f5       f4       f3       f2    e2 | f1 |s|  e1  |
	* ```
	*
	* If big endian (more significant bits first):
	*
	* ```text
	*                         <-- higher      lower -->
	* |s| e1    e2 | f1     f2       f3       f4       f5        f6      f7   |
	* ```
	*
	* In which Uint32 can we find the lower order bits? If little endian, the first; if big endian, the second.
	*
	* ## References
	*
	* -   [Open Group][1]
	*
	* [1]: http://pubs.opengroup.org/onlinepubs/9629399/chap14.htm
	*
	* @param {number} x - double
	* @param {uinteger32} low - unsigned 32-bit integer to replace the lower order word of `x`
	* @returns {number} double having the same higher order word as `x`
	*
	* @example
	* var low = 5 >>> 0; // => 00000000000000000000000000000101
	*
	* var x = 3.14e201; // => 0 11010011100 01001000001011000011 10010011110010110101100010000010
	*
	* var y = setLowWord( x, low ); // => 0 11010011100 01001000001011000011 00000000000000000000000000000101
	* // returns 3.139998651394392e+201
	*
	* @example
	* var PINF = require( '@stdlib/constants-float64-pinf' );
	* var NINF = require( '@stdlib/constants-float64-ninf' );
	*
	* var low = 12345678;
	*
	* var y = setLowWord( PINF, low );
	* // returns NaN
	*
	* y = setLowWord( NINF, low );
	* // returns NaN
	*
	* y = setLowWord( NaN, low );
	* // returns NaN
	*/
	function setLowWord( x, low ) {
		FLOAT64_VIEW[ 0 ] = x;
		UINT32_VIEW[ LOW ] = ( low >>> 0 ); // identity bit shift to ensure integer
		return FLOAT64_VIEW[ 0 ];
	}


	// EXPORTS //

	main$E = setLowWord;
	return main$E;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$K;
var hasRequiredLib$K;

function requireLib$K () {
	if (hasRequiredLib$K) return lib$K;
	hasRequiredLib$K = 1;

	/**
	* Set the less significant 32 bits of a double-precision floating-point number.
	*
	* @module @stdlib/number-float64-base-set-low-word
	*
	* @example
	* var setLowWord = require( '@stdlib/number-float64-base-set-low-word' );
	*
	* var low = 5 >>> 0; // => 00000000000000000000000000000101
	*
	* var x = 3.14e201; // => 0 11010011100 01001000001011000011 10010011110010110101100010000010
	*
	* var y = setLowWord( x, low ); // => 0 11010011100 01001000001011000011 00000000000000000000000000000101
	* // returns 3.139998651394392e+201
	*
	* @example
	* var setLowWord = require( '@stdlib/number-float64-base-set-low-word' );
	* var PINF = require( '@stdlib/constants-float64-pinf' );
	* var NINF = require( '@stdlib/constants-float64-ninf' );
	*
	* var low = 12345678;
	*
	* var y = setLowWord( PINF, low );
	* // returns NaN
	*
	* y = setLowWord( NINF, low );
	* // returns NaN
	*
	* y = setLowWord( NaN, low );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$E();


	// EXPORTS //

	lib$K = main;
	return lib$K;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_pp;
var hasRequiredPolyval_pp;

function requirePolyval_pp () {
	if (hasRequiredPolyval_pp) return polyval_pp;
	hasRequiredPolyval_pp = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return -0.3250421072470015;
		}
		return -0.3250421072470015 + (x * (-0.02848174957559851 + (x * (-0.005770270296489442 + (x * -0.000023763016656650163))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_pp = evalpoly;
	return polyval_pp;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_qq;
var hasRequiredPolyval_qq;

function requirePolyval_qq () {
	if (hasRequiredPolyval_qq) return polyval_qq;
	hasRequiredPolyval_qq = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.39791722395915535;
		}
		return 0.39791722395915535 + (x * (0.0650222499887673 + (x * (0.005081306281875766 + (x * (0.00013249473800432164 + (x * -0.000003960228278775368))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_qq = evalpoly;
	return polyval_qq;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_pa;
var hasRequiredPolyval_pa;

function requirePolyval_pa () {
	if (hasRequiredPolyval_pa) return polyval_pa;
	hasRequiredPolyval_pa = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.41485611868374833;
		}
		return 0.41485611868374833 + (x * (-0.3722078760357013 + (x * (0.31834661990116175 + (x * (-0.11089469428239668 + (x * (0.035478304325618236 + (x * -0.002166375594868791))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_pa = evalpoly;
	return polyval_pa;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_qa;
var hasRequiredPolyval_qa;

function requirePolyval_qa () {
	if (hasRequiredPolyval_qa) return polyval_qa;
	hasRequiredPolyval_qa = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.10642088040084423;
		}
		return 0.10642088040084423 + (x * (0.540397917702171 + (x * (0.07182865441419627 + (x * (0.12617121980876164 + (x * (0.01363708391202905 + (x * 0.011984499846799107))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_qa = evalpoly;
	return polyval_qa;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_ra;
var hasRequiredPolyval_ra;

function requirePolyval_ra () {
	if (hasRequiredPolyval_ra) return polyval_ra;
	hasRequiredPolyval_ra = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return -0.6938585727071818;
		}
		return -0.6938585727071818 + (x * (-10.558626225323291 + (x * (-62.375332450326006 + (x * (-162.39666946257347 + (x * (-184.60509290671104 + (x * (-81.2874355063066 + (x * -9.814329344169145))))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_ra = evalpoly;
	return polyval_ra;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_sa;
var hasRequiredPolyval_sa;

function requirePolyval_sa () {
	if (hasRequiredPolyval_sa) return polyval_sa;
	hasRequiredPolyval_sa = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 19.651271667439257;
		}
		return 19.651271667439257 + (x * (137.65775414351904 + (x * (434.56587747522923 + (x * (645.3872717332679 + (x * (429.00814002756783 + (x * (108.63500554177944 + (x * (6.570249770319282 + (x * -0.0604244152148581))))))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_sa = evalpoly;
	return polyval_sa;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_rb;
var hasRequiredPolyval_rb;

function requirePolyval_rb () {
	if (hasRequiredPolyval_rb) return polyval_rb;
	hasRequiredPolyval_rb = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return -0.799283237680523;
		}
		return -0.799283237680523 + (x * (-17.757954917754752 + (x * (-160.63638485582192 + (x * (-637.5664433683896 + (x * (-1025.0951316110772 + (x * -483.5191916086514))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_rb = evalpoly;
	return polyval_rb;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_sb;
var hasRequiredPolyval_sb;

function requirePolyval_sb () {
	if (hasRequiredPolyval_sb) return polyval_sb;
	hasRequiredPolyval_sb = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 30.33806074348246;
		}
		return 30.33806074348246 + (x * (325.7925129965739 + (x * (1536.729586084437 + (x * (3199.8582195085955 + (x * (2553.0504064331644 + (x * (474.52854120695537 + (x * -22.44095244658582))))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_sb = evalpoly;
	return polyval_sb;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright, license, and long comment were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_erf.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 1993 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var main$D;
var hasRequiredMain$D;

function requireMain$D () {
	if (hasRequiredMain$D) return main$D;
	hasRequiredMain$D = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var exp = requireLib$L();
	var setLowWord = requireLib$K();
	var PINF = requireLib$1m();
	var NINF = requireLib$1n();
	var polyvalPP = requirePolyval_pp();
	var polyvalQQ = requirePolyval_qq();
	var polyvalPA = requirePolyval_pa();
	var polyvalQA = requirePolyval_qa();
	var polyvalRA = requirePolyval_ra();
	var polyvalSA = requirePolyval_sa();
	var polyvalRB = requirePolyval_rb();
	var polyvalSB = requirePolyval_sb();


	// VARIABLES //

	var TINY = 1.0e-300;

	// 2**-56 = 1/(2**56) = 1/72057594037927940
	var SMALL = 1.3877787807814457e-17;

	var ERX = 8.45062911510467529297e-1;  // 0x3FEB0AC1, 0x60000000

	var PPC = 1.28379167095512558561e-1;  // 0x3FC06EBA, 0x8214DB68
	var QQC = 1.0;

	var PAC = -2.36211856075265944077e-3; // 0xBF6359B8, 0xBEF77538
	var QAC = 1.0;

	var RAC = -9.86494403484714822705e-3; // 0xBF843412, 0x600D6435
	var SAC = 1.0;

	var RBC = -9.86494292470009928597e-3; // 0xBF843412, 0x39E86F4A
	var SBC = 1.0;


	// MAIN //

	/**
	* Evaluates the complementary error function.
	*
	* ```tex
	* \operatorname{erf}(x) = \frac{2}{\sqrt{\pi}} \int^{x}_{0} e^{-t^2}\ \mathrm{dt}
	* ```
	*
	* Note that
	*
	* ```tex
	* \begin{align*}
	* \operatorname{erfc}(x) &= 1 - \operatorname{erf}(x) \\
	* \operatorname{erf}(-x) &= -\operatorname{erf}(x) \\
	* \operatorname{erfc}(-x) &= 2 - \operatorname{erfc}(x)
	* \end{align*}
	* ```
	*
	* ## Method
	*
	* 1.  For \\(|x| \in [0, 0.84375)\\),
	*
	*     ```tex
	*     \operatorname{erf}(x) = x + x \cdot \operatorname{R}(x^2)
	*     ```
	*
	*     and
	*
	*     ```tex
	*     \operatorname{erfc}(x) = \begin{cases}
	*     1 - \operatorname{erf}(x) & \textrm{if}\ x \in (-.84375,0.25) \\
	*     0.5 + ((0.5-x)-x \mathrm{R}) & \textrm{if}\ x \in [0.25,0.84375)
	*     \end{cases}
	*     ```
	*
	*     where \\(R = P/Q\\) and where \\(P\\) is an odd polynomial of degree \\(8\\) and \\(Q\\) is an odd polynomial of degree \\(10\\).
	*
	*     ```tex
	*     \biggl| \mathrm{R} - \frac{\operatorname{erf}(x)-x}{x} \biggr| \leq 2^{-57.90}
	*     ```
	*
	*     <!-- <note> -->
	*
	*     The formula is derived by noting
	*
	*     ```tex
	*     \operatorname{erf}(x) = \frac{2}{\sqrt{\pi}}\biggl(x - \frac{x^3}{3} + \frac{x^5}{10} - \frac{x^7}{42} + \ldots \biggr)
	*     ```
	*
	*     and that
	*
	*     ```tex
	*     \frac{2}{\sqrt{\pi}} = 1.128379167095512573896158903121545171688
	*     ```
	*
	*     is close to unity. The interval is chosen because the fix point of \\(\operatorname{erf}(x)\\) is near \\(0.6174\\) (i.e., \\(\operatorname{erf(x)} = x\\) when \\(x\\) is near \\(0.6174\\)), and, by some experiment, \\(0.84375\\) is chosen to guarantee the error is less than one ulp for \\(\operatorname{erf}(x)\\).
	*
	*     <!-- </note> -->
	*
	* 2.  For \\(|x| \in [0.84375,1.25)\\), let \\(s = |x|-1\\), and \\(c = 0.84506291151\\) rounded to single (\\(24\\) bits)
	*
	*     ```tex
	*     \operatorname{erf}(x) = \operatorname{sign}(x) \cdot \biggl(c + \frac{\operatorname{P1}(s)}{\operatorname{Q1}(s)}\biggr)
	*     ```
	*
	*     and
	*
	*     ```tex
	*     \operatorname{erfc}(x) = \begin{cases}
	*     (1-c) - \frac{\operatorname{P1}(s)}{\operatorname{Q1}(s)} & \textrm{if}\ x > 0 \\
	*     1 + \biggl(c + \frac{\operatorname{P1}(s)}{\operatorname{Q1}(s)}\biggr) & \textrm{if}\ x < 0
	*     \end{cases}
	*     ```
	*
	*     where
	*
	*     ```tex
	*     \biggl|\frac{\mathrm{P1}}{\mathrm{Q1}} - (\operatorname{erf}(|x|)-c)\biggr| \leq 2^{-59.06}
	*     ```
	*
	*     <!-- <note> -->
	*
	*     Here, we use the Taylor series expansion at \\(x = 1\\)
	*
	*     ```tex
	*     \begin{align*}
	*     \operatorname{erf}(1+s) &= \operatorname{erf}(1) + s\cdot \operatorname{poly}(s) \\
	*     &= 0.845.. + \frac{\operatorname{P1}(s)}{\operatorname{Q1}(s)}
	*     \end{align*}
	*     ```
	*
	*     using a rational approximation to approximate
	*
	*     ```tex
	*     \operatorname{erf}(1+s) - (c = (\mathrm{single})0.84506291151)
	*     ```
	*
	*     <!-- </note> -->
	*
	*     Note that, for \\(x \in [0.84375,1.25)\\), \\(|\mathrm{P1}/\mathrm{Q1}| < 0.078\\), where
	*
	*     -   \\(\operatorname{P1}(s)\\) is a degree \\(6\\) polynomial in \\(s\\)
	*     -   \\(\operatorname{Q1}(s)\\) is a degree \\(6\\) polynomial in \\(s\\)
	*
	* 3.  For \\(x \in [1.25,1/0.35)\\),
	*
	*     ```tex
	*     \begin{align*}
	*     \operatorname{erfc}(x) &= \frac{1}{x}e^{-x^2-0.5625+(\mathrm{R1}/\mathrm{S1})} \\
	*     \operatorname{erf}(x) &= 1 - \operatorname{erfc}(x)
	*     \end{align*}
	*     ```
	*
	*     where
	*
	*     -   \\(\operatorname{R1}(z)\\) is a degree \\(7\\) polynomial in \\(z\\), where \\(z = 1/x^2\\)
	*     -   \\(\operatorname{S1}(z)\\) is a degree \\(8\\) polynomial in \\(z\\)
	*
	* 4.  For \\(x \in [1/0.35,28)\\),
	*
	*     ```tex
	*     \operatorname{erfc}(x) = \begin{cases}
	*     \frac{1}{x} e^{-x^2-0.5625+(\mathrm{R2}/\mathrm{S2})} & \textrm{if}\ x > 0 \\
	*     2.0 - \frac{1}{x} e^{-x^2-0.5625+(\mathrm{R2}/\mathrm{S2})} & \textrm{if}\ -6 < x < 0 \\
	*     2.0 - \mathrm{tiny} & \textrm{if}\ x \leq -6
	*     \end{cases}
	*     ```
	*
	*     and
	*
	*     ```tex
	*     \operatorname{erf}(x) = \begin{cases}
	*     \operatorname{sign}(x) \cdot (1.0 - \operatorname{erfc}(x)) & \textrm{if}\ x < 6 \\
	*     \operatorname{sign}(x) \cdot (1.0 - \mathrm{tiny}) & \textrm{otherwise}
	*     \end{cases}
	*     ```
	*
	*     where
	*
	*     -   \\(\operatorname{R2}(z)\\) is a degree \\(6\\) polynomial in \\(z\\), where \\(z = 1/x^2\\)
	*     -   \\(\operatorname{S2}(z)\\) is a degree \\(7\\) polynomial in \\(z\\)
	*
	* 5.  For \\(x \in [28, \infty)\\),
	*
	*     ```tex
	*     \begin{align*}
	*     \operatorname{erf}(x) &= \operatorname{sign}(x) \cdot (1 - \mathrm{tiny}) & \textrm{(raise inexact)}
	*     \end{align*}
	*     ```
	*
	*     and
	*
	*     ```tex
	*     \operatorname{erfc}(x) = \begin{cases}
	*     \mathrm{tiny} \cdot \mathrm{tiny} & \textrm{if}\ x > 0\ \textrm{(raise underflow)} \\
	*     2 - \mathrm{tiny} & \textrm{if}\ x < 0
	*     \end{cases}
	*     ```
	*
	* ## Special Cases
	*
	* ```tex
	* \begin{align*}
	* \operatorname{erf}(0) &= 0 \\
	* \operatorname{erf}(-0) &= -0 \\
	* \operatorname{erf}(\infty) &= 1 \\
	* \operatorname{erf}(-\infty) &= -1 \\
	* \operatorname{erfc}(0) &= 1 \\
	* \operatorname{erfc}(\infty) &= 0 \\
	* \operatorname{erfc}(-\infty) &= 2 \\
	* \operatorname{erf}(\mathrm{NaN}) &= \mathrm{NaN} \\
	* \operatorname{erfc}(\mathrm{NaN}) &= \mathrm{NaN}
	* \end{align*}
	* ```
	*
	* ## Notes
	*
	* -   To compute \\(\exp(-x^2-0.5625+(\mathrm{R}/\mathrm{S}))\\), let \\(s\\) be a single precision number and \\(s := x\\); then
	*
	*     ```tex
	*     -x^2 = -s^2 + (s-x)(s+x)
	*     ```
	*
	*     and
	*
	*     ```tex
	*     e^{-x^2-0.5626+(\mathrm{R}/\mathrm{S})} = e^{-s^2-0.5625} e^{(s-x)(s+x)+(\mathrm{R}/\mathrm{S})}
	*     ```
	*
	* -   `#4` and `#5` make use of the asymptotic series
	*
	*     ```tex
	*     \operatorname{erfc}(x) \approx \frac{e^{-x^2}}{x\sqrt{\pi}} (1 + \operatorname{poly}(1/x^2))
	*     ```
	*
	*     We use a rational approximation to approximate
	*
	*     ```tex
	*     g(s) = f(1/x^2) = \ln(\operatorname{erfc}(x) \cdot x) - x^2 + 0.5625
	*     ```
	*
	* -   The error bound for \\(\mathrm{R1}/\mathrm{S1}\\) is
	*
	*     ```tex
	*     |\mathrm{R1}/\mathrm{S1} - f(x)| < 2^{-62.57}
	*     ```
	*
	*     and for \\(\mathrm{R2}/\mathrm{S2}\\) is
	*
	*     ```tex
	*     |\mathrm{R2}/\mathrm{S2} - f(x)| < 2^{-61.52}
	*     ```
	*
	* @param {number} x - input value
	* @returns {number} function value
	*
	* @example
	* var y = erfc( 2.0 );
	* // returns ~0.0047
	*
	* @example
	* var y = erfc( -1.0 );
	* // returns ~1.8427
	*
	* @example
	* var y = erfc( 0.0 );
	* // returns 1.0
	*
	* @example
	* var y = erfc( Infinity );
	* // returns 0.0
	*
	* @example
	* var y = erfc( -Infinity );
	* // returns 2.0
	*
	* @example
	* var y = erfc( NaN );
	* // returns NaN
	*/
	function erfc( x ) {
		var sign;
		var ax;
		var z;
		var r;
		var s;
		var y;
		var p;
		var q;

		// Special case: NaN
		if ( isnan( x ) ) {
			return NaN;
		}
		// Special case: +infinity
		if ( x === PINF ) {
			return 0.0;
		}
		// Special case: -infinity
		if ( x === NINF ) {
			return 2.0;
		}
		// Special case: +-0
		if ( x === 0.0 ) {
			return 1.0;
		}
		if ( x < 0.0 ) {
			sign = true;
			ax = -x;
		} else {
			sign = false;
			ax = x;
		}
		// |x| < 0.84375
		if ( ax < 0.84375 ) {
			if ( ax < SMALL ) {
				return 1.0 - x; // raise inexact
			}
			z = x * x;
			r = PPC + ( z*polyvalPP( z ) );
			s = QQC + ( z*polyvalQQ( z ) );
			y = r / s;

			// x < 1/4
			if ( x < 0.25 ) {
				return 1.0 - ( x + (x*y) );
			}
			r = x * y;
			r += x - 0.5;
			return 0.5 - r;
		}
		// 0.84375 <= |x| < 1.25
		if ( ax < 1.25 ) {
			s = ax - 1.0;
			p = PAC + ( s*polyvalPA( s ) );
			q = QAC + ( s*polyvalQA( s ) );
			if ( sign ) {
				return 1.0 + ERX + (p/q);
			}
			return 1.0 - ERX - (p/q);
		}
		// |x| < 28
		if ( ax < 28.0 ) {
			s = 1.0 / (ax*ax);

			// |x| < 1/0.35 ~ 2.857143
			if ( ax < 2.857142857142857 ) {
				r = RAC + ( s*polyvalRA( s ) );
				s = SAC + ( s*polyvalSA( s ) );
			}
			// |x| >= 1/0.35 ~ 2.857143
			else {
				// x < -6
				if ( x < -6.0 ) {
					return 2.0 - TINY; // raise inexact
				}
				r = RBC + ( s*polyvalRB( s ) );
				s = SBC + ( s*polyvalSB( s ) );
			}
			z = setLowWord( ax, 0 ); // pseudo-single (20-bit) precision x
			r = exp( -(z*z) - 0.5625 ) * exp( ((z-ax)*(z+ax)) + (r/s) );
			if ( sign ) {
				return 2.0 - (r/ax);
			}
			return r/ax;
		}
		if ( sign ) {
			return 2.0 - TINY; // raise inexact; ~2
		}
		return TINY * TINY; // raise inexact; ~0
	}


	// EXPORTS //

	main$D = erfc;
	return main$D;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$J;
var hasRequiredLib$J;

function requireLib$J () {
	if (hasRequiredLib$J) return lib$J;
	hasRequiredLib$J = 1;

	/**
	* Evaluate the complementary error function.
	*
	* @module @stdlib/math-base-special-erfc
	*
	* @example
	* var erfc = require( '@stdlib/math-base-special-erfc' );
	*
	* var y = erfc( 2.0 );
	* // returns ~0.0047
	*
	* y = erfc( -1.0 );
	* // returns ~1.8427
	*
	* y = erfc( 0.0 );
	* // returns 1.0
	*
	* y = erfc( Infinity );
	* // returns 0.0
	*
	* y = erfc( -Infinity );
	* // returns 2.0
	*
	* y = erfc( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$D();


	// EXPORTS //

	lib$J = main;
	return lib$J;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$C;
var hasRequiredMain$C;

function requireMain$C () {
	if (hasRequiredMain$C) return main$C;
	hasRequiredMain$C = 1;

	// MAIN //

	/**
	* Compute the principal square root of a double-precision floating-point number.
	*
	* @type {Function}
	* @param {number} x - input value
	* @returns {number} principal square root
	*
	* @example
	* var v = sqrt( 4.0 );
	* // returns 2.0
	*
	* v = sqrt( 9.0 );
	* // returns 3.0
	*
	* v = sqrt( 0.0 );
	* // returns 0.0
	*
	* v = sqrt( -4.0 );
	* // returns NaN
	*
	* v = sqrt( NaN );
	* // returns NaN
	*/
	var sqrt = Math.sqrt; // eslint-disable-line stdlib/no-builtin-math


	// EXPORTS //

	main$C = sqrt;
	return main$C;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$I;
var hasRequiredLib$I;

function requireLib$I () {
	if (hasRequiredLib$I) return lib$I;
	hasRequiredLib$I = 1;

	/**
	* Compute the principal square root of a double-precision floating-point number.
	*
	* @module @stdlib/math-base-special-sqrt
	*
	* @example
	* var sqrt = require( '@stdlib/math-base-special-sqrt' );
	*
	* var v = sqrt( 4.0 );
	* // returns 2.0
	*
	* v = sqrt( 9.0 );
	* // returns 3.0
	*
	* v = sqrt( 0.0 );
	* // returns 0.0
	*
	* v = sqrt( -4.0 );
	* // returns NaN
	*
	* v = sqrt( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$C();


	// EXPORTS //

	lib$I = main;
	return lib$I;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$B;
var hasRequiredMain$B;

function requireMain$B () {
	if (hasRequiredMain$B) return main$B;
	hasRequiredMain$B = 1;

	// MODULES //

	var erfc = requireLib$J();
	var sqrt = requireLib$I();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the cumulative distribution function (CDF) for a Normal distribution with mean `mu` and standard deviation `sigma` at a value `x`.
	*
	* @param {number} x - input value
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {Probability} evaluated cumulative distribution function
	*
	* @example
	* var y = cdf( 2.0, 0.0, 1.0 );
	* // returns ~0.977
	*
	* @example
	* var y = cdf( -1.0, -1.0, 2.0 );
	* // returns 0.5
	*
	* @example
	* var y = cdf( -1.0, 4.0, 2.0 );
	* // returns ~0.006
	*
	* @example
	* var y = cdf( NaN, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = cdf( 0.0, NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = cdf( 0.0, 0.0, NaN );
	* // returns NaN
	*
	* @example
	* // Negative standard deviation:
	* var y = cdf( 2.0, 0.0, -1.0 );
	* // returns NaN
	*/
	function cdf( x, mu, sigma ) {
		var denom;
		var xc;
		if (
			isnan( x ) ||
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return NaN;
		}
		if ( sigma === 0.0 ) {
			return (x < mu) ? 0.0 : 1.0;
		}
		denom = sigma * sqrt( 2.0 );
		xc = x - mu;
		return 0.5 * erfc( -xc/denom );
	}


	// EXPORTS //

	main$B = cdf;
	return main$B;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$A;
var hasRequiredMain$A;

function requireMain$A () {
	if (hasRequiredMain$A) return main$A;
	hasRequiredMain$A = 1;

	/**
	* Creates a function which always returns the same value.
	*
	* @param {*} [value] - value to always return
	* @returns {Function} constant function
	*
	* @example
	* var fcn = wrap( 3.14 );
	*
	* var v = fcn();
	* // returns 3.14
	*
	* v = fcn();
	* // returns 3.14
	*
	* v = fcn();
	* // returns 3.14
	*/
	function wrap( value ) {
		return constantFunction;

		/**
		* Constant function.
		*
		* @private
		* @returns {*} constant value
		*/
		function constantFunction() {
			return value;
		}
	}


	// EXPORTS //

	main$A = wrap;
	return main$A;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$H;
var hasRequiredLib$H;

function requireLib$H () {
	if (hasRequiredLib$H) return lib$H;
	hasRequiredLib$H = 1;

	/**
	* Create a constant function.
	*
	* @module @stdlib/utils-constant-function
	*
	* @example
	* var constantFunction = require( '@stdlib/utils-constant-function' );
	*
	* var fcn = constantFunction( 3.14 );
	*
	* var v = fcn();
	* // returns 3.14
	*
	* v = fcn();
	* // returns 3.14
	*
	* v = fcn();
	* // returns 3.14
	*/

	// MODULES //

	var main = requireMain$A();


	// EXPORTS //

	lib$H = main;
	return lib$H;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$z;
var hasRequiredMain$z;

function requireMain$z () {
	if (hasRequiredMain$z) return main$z;
	hasRequiredMain$z = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the cumulative distribution function (CDF) for a degenerate distribution with mean value `mu`.
	*
	* @param {number} x - input value
	* @param {number} mu - constant value of distribution
	* @returns {Probability} evaluated cumulative distribution function
	*
	* @example
	* var y = cdf( 2.0, 3.0 );
	* // returns 0.0
	*
	* @example
	* var y = cdf( 4.0, 3.0 );
	* // returns 1.0
	*
	* @example
	* var y = cdf( 3.0, 3.0 );
	* // returns 1.0
	*
	* @example
	* var y = cdf( NaN, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = cdf( 0.0, NaN );
	* // returns NaN
	*/
	function cdf( x, mu ) {
		if ( isnan( x ) || isnan( mu ) ) {
			return NaN;
		}
		return (x < mu) ? 0.0 : 1.0;
	}


	// EXPORTS //

	main$z = cdf;
	return main$z;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$a;
var hasRequiredFactory$a;

function requireFactory$a () {
	if (hasRequiredFactory$a) return factory_1$a;
	hasRequiredFactory$a = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns a function for evaluating the cumulative distribution function (CDF) of a degenerate distribution centered at a provided mean value.
	*
	* @param {number} mu - constant value of distribution
	* @returns {Function} function to evaluate the cumulative distribution function
	*
	* @example
	* var cdf = factory( 5.0 );
	*
	* var y = cdf( 3.0 );
	* // returns 0.0
	*
	* y = cdf( 6.0 );
	* // returns 1.0
	*
	* y = cdf( NaN );
	* // returns NaN
	*/
	function factory( mu ) {
		if ( isnan( mu ) ) {
			return constantFunction( NaN );
		}
		return cdf;

		/**
		* Evaluates the cumulative distribution function (CDF) of a degenerate distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {Probability} evaluated cumulative distribution function
		*
		* @example
		* var y = cdf( 10.0 );
		* // returns <number>
		*/
		function cdf( x ) {
			if ( isnan( x ) ) {
				return NaN;
			}
			return (x < mu) ? 0.0 : 1.0;
		}
	}


	// EXPORTS //

	factory_1$a = factory;
	return factory_1$a;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$G;
var hasRequiredLib$G;

function requireLib$G () {
	if (hasRequiredLib$G) return lib$G;
	hasRequiredLib$G = 1;

	/**
	* Degenerate distribution cumulative distribution function (CDF).
	*
	* @module @stdlib/stats-base-dists-degenerate-cdf
	*
	* @example
	* var cdf = require( '@stdlib/stats-base-dists-degenerate-cdf' );
	*
	* var y = cdf( 2.0, 5.0 );
	* // returns 0.0
	*
	* @example
	* var factory = require( '@stdlib/stats-base-dists-degenerate-cdf' ).factory;
	*
	* var cdf = factory( 5.0 );
	*
	* var y = cdf( 3.0 );
	* // returns 0.0
	*
	* y = cdf( 6.0 );
	* // returns 1.0
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$z();
	var factory = requireFactory$a();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$G = main;
	return lib$G;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$9;
var hasRequiredFactory$9;

function requireFactory$9 () {
	if (hasRequiredFactory$9) return factory_1$9;
	hasRequiredFactory$9 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var degenerate = requireLib$G().factory;
	var isnan = requireLib$1s();
	var sqrt = requireLib$I();
	var erfc = requireLib$J();


	// MAIN //

	/**
	* Returns a function for evaluating the cumulative distribution function (CDF) for a Normal distribution.
	*
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {Function} function to evaluate the cumulative distribution function
	*
	* @example
	* var cdf = factory( 10.0, 2.0 );
	* var y = cdf( 10.0 );
	* // returns 0.5
	*
	* y = cdf( 12.0 );
	* // returns ~0.841
	*/
	function factory( mu, sigma ) {
		var denom;
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return constantFunction( NaN );
		}
		if ( sigma === 0.0 ) {
			return degenerate( mu );
		}
		denom = sigma * sqrt( 2.0 );
		return cdf;

		/**
		* Evaluates the cumulative distribution function (CDF) for a Normal distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {Probability} evaluated CDF
		*
		* @example
		* var y = cdf( 2.0 );
		* // returns <number>
		*/
		function cdf( x ) {
			var xc;
			if ( isnan( x ) ) {
				return NaN;
			}
			xc = x - mu;
			return 0.5 * erfc( -xc / denom );
		}
	}


	// EXPORTS //

	factory_1$9 = factory;
	return factory_1$9;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$F;
var hasRequiredLib$F;

function requireLib$F () {
	if (hasRequiredLib$F) return lib$F;
	hasRequiredLib$F = 1;

	/**
	* Normal distribution cumulative distribution function (CDF).
	*
	* @module @stdlib/stats-base-dists-normal-cdf
	*
	* @example
	* var cdf = require( '@stdlib/stats-base-dists-normal-cdf' );
	*
	* var y = cdf( 2.0, 0.0, 1.0 );
	* // returns ~0.977
	*
	* var myCDF = cdf.factory( 10.0, 2.0 );
	* y = myCDF( 10.0 );
	* // returns 0.5
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$B();
	var factory = requireFactory$9();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$F = main;
	return lib$F;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$y;
var hasRequiredMain$y;

function requireMain$y () {
	if (hasRequiredMain$y) return main$y;
	hasRequiredMain$y = 1;

	// MODULES //

	var defineProperty = requireLib$1v();


	// MAIN //

	/**
	* Defines a non-enumerable read-only accessor.
	*
	* @param {Object} obj - object on which to define the property
	* @param {(string|symbol)} prop - property name
	* @param {Function} getter - accessor
	*
	* @example
	* function getter() {
	*     return 'bar';
	* }
	*
	* var obj = {};
	*
	* setNonEnumerableReadOnlyAccessor( obj, 'foo', getter );
	*
	* try {
	*     obj.foo = 'boop';
	* } catch ( err ) {
	*     console.error( err.message );
	* }
	*/
	function setNonEnumerableReadOnlyAccessor( obj, prop, getter ) { // eslint-disable-line id-length
		defineProperty( obj, prop, {
			'configurable': false,
			'enumerable': false,
			'get': getter
		});
	}


	// EXPORTS //

	main$y = setNonEnumerableReadOnlyAccessor;
	return main$y;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$E;
var hasRequiredLib$E;

function requireLib$E () {
	if (hasRequiredLib$E) return lib$E;
	hasRequiredLib$E = 1;

	/**
	* Define a non-enumerable read-only accessor.
	*
	* @module @stdlib/utils-define-nonenumerable-read-only-accessor
	*
	* @example
	* var setNonEnumerableReadOnlyAccessor = require( '@stdlib/utils-define-nonenumerable-read-only-accessor' );
	*
	* function getter() {
	*     return 'bar';
	* }
	*
	* var obj = {};
	*
	* setNonEnumerableReadOnlyAccessor( obj, 'foo', getter );
	*
	* try {
	*     obj.foo = 'boop';
	* } catch ( err ) {
	*     console.error( err.message );
	* }
	*/

	// MODULES //

	var main = requireMain$y();


	// EXPORTS //

	lib$E = main;
	return lib$E;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var primitive$2;
var hasRequiredPrimitive$2;

function requirePrimitive$2 () {
	if (hasRequiredPrimitive$2) return primitive$2;
	hasRequiredPrimitive$2 = 1;

	/**
	* Tests if a value is a number primitive.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a number primitive
	*
	* @example
	* var bool = isNumber( 3.14 );
	* // returns true
	*
	* @example
	* var bool = isNumber( NaN );
	* // returns true
	*
	* @example
	* var bool = isNumber( new Number( 3.14 ) );
	* // returns false
	*/
	function isNumber( value ) {
		return ( typeof value === 'number' );
	}


	// EXPORTS //

	primitive$2 = isNumber;
	return primitive$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var tostring;
var hasRequiredTostring;

function requireTostring () {
	if (hasRequiredTostring) return tostring;
	hasRequiredTostring = 1;

	// MODULES //

	var Number = requireLib$1o();


	// MAIN //

	// eslint-disable-next-line stdlib/no-redeclare
	var toString = Number.prototype.toString; // non-generic


	// EXPORTS //

	tostring = toString;
	return tostring;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var try2serialize;
var hasRequiredTry2serialize;

function requireTry2serialize () {
	if (hasRequiredTry2serialize) return try2serialize;
	hasRequiredTry2serialize = 1;

	// MODULES //

	var toString = requireTostring(); // eslint-disable-line stdlib/no-redeclare


	// MAIN //

	/**
	* Attempts to serialize a value to a string.
	*
	* @private
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value can be serialized
	*/
	function test( value ) {
		try {
			toString.call( value );
			return true;
		} catch ( err ) { // eslint-disable-line no-unused-vars
			return false;
		}
	}


	// EXPORTS //

	try2serialize = test;
	return try2serialize;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var object$2;
var hasRequiredObject$2;

function requireObject$2 () {
	if (hasRequiredObject$2) return object$2;
	hasRequiredObject$2 = 1;

	// MODULES //

	var hasToStringTag = requireLib$1d();
	var nativeClass = requireLib$1a();
	var Number = requireLib$1o();
	var test = requireTry2serialize();


	// VARIABLES //

	var FLG = hasToStringTag();


	// MAIN //

	/**
	* Tests if a value is a number object.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a number object
	*
	* @example
	* var bool = isNumber( 3.14 );
	* // returns false
	*
	* @example
	* var bool = isNumber( new Number( 3.14 ) );
	* // returns true
	*/
	function isNumber( value ) {
		if ( typeof value === 'object' ) {
			if ( value instanceof Number ) {
				return true;
			}
			if ( FLG ) {
				return test( value );
			}
			return ( nativeClass( value ) === '[object Number]' );
		}
		return false;
	}


	// EXPORTS //

	object$2 = isNumber;
	return object$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$x;
var hasRequiredMain$x;

function requireMain$x () {
	if (hasRequiredMain$x) return main$x;
	hasRequiredMain$x = 1;

	// MODULES //

	var isPrimitive = requirePrimitive$2();
	var isObject = requireObject$2();


	// MAIN //

	/**
	* Tests if a value is a number.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is a number
	*
	* @example
	* var bool = isNumber( 3.14 );
	* // returns true
	*
	* @example
	* var bool = isNumber( new Number( 3.14 ) );
	* // returns true
	*
	* @example
	* var bool = isNumber( NaN );
	* // returns true
	*
	* @example
	* var bool = isNumber( null );
	* // returns false
	*/
	function isNumber( value ) {
		return ( isPrimitive( value ) || isObject( value ) );
	}


	// EXPORTS //

	main$x = isNumber;
	return main$x;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$D;
var hasRequiredLib$D;

function requireLib$D () {
	if (hasRequiredLib$D) return lib$D;
	hasRequiredLib$D = 1;

	/**
	* Test if a value is a number.
	*
	* @module @stdlib/assert-is-number
	*
	* @example
	* var isNumber = require( '@stdlib/assert-is-number' );
	*
	* var bool = isNumber( 3.14 );
	* // returns true
	*
	* bool = isNumber( new Number( 3.14 ) );
	* // returns true
	*
	* bool = isNumber( NaN );
	* // returns true
	*
	* bool = isNumber( null );
	* // returns false
	*
	* @example
	* var isNumber = require( '@stdlib/assert-is-number' ).isPrimitive;
	*
	* var bool = isNumber( 3.14 );
	* // returns true
	*
	* bool = isNumber( NaN );
	* // returns true
	*
	* bool = isNumber( new Number( 3.14 ) );
	* // returns false
	*
	* @example
	* var isNumber = require( '@stdlib/assert-is-number' ).isObject;
	*
	* var bool = isNumber( 3.14 );
	* // returns false
	*
	* bool = isNumber( new Number( 3.14 ) );
	* // returns true
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$x();
	var isPrimitive = requirePrimitive$2();
	var isObject = requireObject$2();


	// MAIN //

	setReadOnly( main, 'isPrimitive', isPrimitive );
	setReadOnly( main, 'isObject', isObject );


	// EXPORTS //

	lib$D = main;
	return lib$D;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var primitive$1;
var hasRequiredPrimitive$1;

function requirePrimitive$1 () {
	if (hasRequiredPrimitive$1) return primitive$1;
	hasRequiredPrimitive$1 = 1;

	// MODULES //

	var isNumber = requireLib$D().isPrimitive;


	// MAIN //

	/**
	* Tests if a value is a number primitive having a positive value.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a number primitive having a positive value
	*
	* @example
	* var bool = isPositiveNumber( 3.0 );
	* // returns true
	*
	* @example
	* var bool = isPositiveNumber( new Number( 3.0 ) );
	* // returns false
	*/
	function isPositiveNumber( value ) {
		return (
			isNumber( value ) &&
			value > 0.0
		);
	}


	// EXPORTS //

	primitive$1 = isPositiveNumber;
	return primitive$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var object$1;
var hasRequiredObject$1;

function requireObject$1 () {
	if (hasRequiredObject$1) return object$1;
	hasRequiredObject$1 = 1;

	// MODULES //

	var isNumber = requireLib$D().isObject;


	// MAIN //

	/**
	* Tests if a value is a number object having a positive value.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a number object having a positive value
	*
	* @example
	* var bool = isPositiveNumber( 3.0 );
	* // returns false
	*
	* @example
	* var bool = isPositiveNumber( new Number( 3.0 ) );
	* // returns true
	*/
	function isPositiveNumber( value ) {
		return (
			isNumber( value ) &&
			value.valueOf() > 0.0
		);
	}


	// EXPORTS //

	object$1 = isPositiveNumber;
	return object$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$w;
var hasRequiredMain$w;

function requireMain$w () {
	if (hasRequiredMain$w) return main$w;
	hasRequiredMain$w = 1;

	// MODULES //

	var isPrimitive = requirePrimitive$1();
	var isObject = requireObject$1();


	// MAIN //

	/**
	* Tests if a value is a positive number.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is a positive number
	*
	* @example
	* var bool = isPositiveNumber( 5.0 );
	* // returns true
	*
	* @example
	* var bool = isPositiveNumber( new Number( 5.0 ) );
	* // returns true
	*
	* @example
	* var bool = isPositiveNumber( 3.14 );
	* // returns true
	*
	* @example
	* var bool = isPositiveNumber( -5.0 );
	* // returns false
	*
	* @example
	* var bool = isPositiveNumber( null );
	* // returns false
	*/
	function isPositiveNumber( value ) {
		return ( isPrimitive( value ) || isObject( value ) );
	}


	// EXPORTS //

	main$w = isPositiveNumber;
	return main$w;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$C;
var hasRequiredLib$C;

function requireLib$C () {
	if (hasRequiredLib$C) return lib$C;
	hasRequiredLib$C = 1;

	/**
	* Test if a value is a positive number.
	*
	* @module @stdlib/assert-is-positive-number
	*
	* @example
	* var isPositiveNumber = require( '@stdlib/assert-is-positive-number' );
	*
	* var bool = isPositiveNumber( 5.0 );
	* // returns true
	*
	* bool = isPositiveNumber( new Number( 5.0 ) );
	* // returns true
	*
	* bool = isPositiveNumber( 3.14 );
	* // returns true
	*
	* bool = isPositiveNumber( -5.0 );
	* // returns false
	*
	* bool = isPositiveNumber( null );
	* // returns false
	*
	* @example
	* var isPositiveNumber = require( '@stdlib/assert-is-positive-number' ).isPrimitive;
	*
	* var bool = isPositiveNumber( 3.0 );
	* // returns true
	*
	* bool = isPositiveNumber( new Number( 3.0 ) );
	* // returns false
	*
	* @example
	* var isPositiveNumber = require( '@stdlib/assert-is-positive-number' ).isObject;
	*
	* var bool = isPositiveNumber( 3.0 );
	* // returns false
	*
	* bool = isPositiveNumber( new Number( 3.0 ) );
	* // returns true
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$w();
	var isPrimitive = requirePrimitive$1();
	var isObject = requireObject$1();


	// MAIN //

	setReadOnly( main, 'isPrimitive', isPrimitive );
	setReadOnly( main, 'isObject', isObject );


	// EXPORTS //

	lib$C = main;
	return lib$C;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var primitive;
var hasRequiredPrimitive;

function requirePrimitive () {
	if (hasRequiredPrimitive) return primitive;
	hasRequiredPrimitive = 1;

	// MODULES //

	var isNumber = requireLib$D().isPrimitive;
	var isNan = requireLib$1s();


	// MAIN //

	/**
	* Tests if a value is a `NaN` number primitive.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a `NaN` number primitive
	*
	* @example
	* var bool = isnan( NaN );
	* // returns true
	*
	* @example
	* var bool = isnan( 3.14 );
	* // returns false
	*
	* @example
	* var bool = isnan( new Number( NaN ) );
	* // returns false
	*/
	function isnan( value ) {
		return (
			isNumber( value ) &&
			isNan( value )
		);
	}


	// EXPORTS //

	primitive = isnan;
	return primitive;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var object;
var hasRequiredObject;

function requireObject () {
	if (hasRequiredObject) return object;
	hasRequiredObject = 1;

	// MODULES //

	var isNumber = requireLib$D().isObject;
	var isNan = requireLib$1s();


	// MAIN //

	/**
	* Tests if a value is a number object having a value of `NaN`.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating if a value is a number object having a value of `NaN`
	*
	* @example
	* var bool = isnan( NaN );
	* // returns false
	*
	* @example
	* var bool = isnan( new Number( NaN ) );
	* // returns true
	*/
	function isnan( value ) {
		return (
			isNumber( value ) &&
			isNan( value.valueOf() )
		);
	}


	// EXPORTS //

	object = isnan;
	return object;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$v;
var hasRequiredMain$v;

function requireMain$v () {
	if (hasRequiredMain$v) return main$v;
	hasRequiredMain$v = 1;

	// MODULES //

	var isPrimitive = requirePrimitive();
	var isObject = requireObject();


	// MAIN //

	/**
	* Tests if a value is `NaN`.
	*
	* @param {*} value - value to test
	* @returns {boolean} boolean indicating whether value is `NaN`
	*
	* @example
	* var bool = isnan( NaN );
	* // returns true
	*
	* @example
	* var bool = isnan( new Number( NaN ) );
	* // returns true
	*
	* @example
	* var bool = isnan( 3.14 );
	* // returns false
	*
	* @example
	* var bool = isnan( null );
	* // returns false
	*/
	function isnan( value ) {
		return ( isPrimitive( value ) || isObject( value ) );
	}


	// EXPORTS //

	main$v = isnan;
	return main$v;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$B;
var hasRequiredLib$B;

function requireLib$B () {
	if (hasRequiredLib$B) return lib$B;
	hasRequiredLib$B = 1;

	/**
	* Test if a value is `NaN`.
	*
	* @module @stdlib/assert-is-nan
	*
	* @example
	* var isnan = require( '@stdlib/assert-is-nan' );
	*
	* var bool = isnan( NaN );
	* // returns true
	*
	* bool = isnan( new Number( NaN ) );
	* // returns true
	*
	* bool = isnan( 3.14 );
	* // returns false
	*
	* bool = isnan( null );
	* // returns false
	*
	* @example
	* var isnan = require( '@stdlib/assert-is-nan' ).isPrimitive;
	*
	* var bool = isnan( NaN );
	* // returns true
	*
	* bool = isnan( 3.14 );
	* // returns false
	*
	* bool = isnan( new Number( NaN ) );
	* // returns false
	*
	* @example
	* var isnan = require( '@stdlib/assert-is-nan' ).isObject;
	*
	* var bool = isnan( NaN );
	* // returns false
	*
	* bool = isnan( new Number( NaN ) );
	* // returns true
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$v();
	var isPrimitive = requirePrimitive();
	var isObject = requireObject();


	// MAIN //

	setReadOnly( main, 'isPrimitive', isPrimitive );
	setReadOnly( main, 'isObject', isObject );


	// EXPORTS //

	lib$B = main;
	return lib$B;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var high;
var hasRequiredHigh;

function requireHigh () {
	if (hasRequiredHigh) return high;
	hasRequiredHigh = 1;

	// MODULES //

	var isLittleEndian = requireLib$W();


	// MAIN //

	var HIGH;
	if ( isLittleEndian === true ) {
		HIGH = 1; // second index
	} else {
		HIGH = 0; // first index
	}


	// EXPORTS //

	high = HIGH;
	return high;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$u;
var hasRequiredMain$u;

function requireMain$u () {
	if (hasRequiredMain$u) return main$u;
	hasRequiredMain$u = 1;

	// MODULES //

	var Uint32Array = requireLib$16();
	var Float64Array = requireLib$13();
	var HIGH = requireHigh();


	// VARIABLES //

	var FLOAT64_VIEW = new Float64Array( 1 );
	var UINT32_VIEW = new Uint32Array( FLOAT64_VIEW.buffer );


	// MAIN //

	/**
	* Sets the more significant 32 bits of a double-precision floating-point number.
	*
	* ## Notes
	*
	* ```text
	* float64 (64 bits)
	* f := fraction (significand/mantissa) (52 bits)
	* e := exponent (11 bits)
	* s := sign bit (1 bit)
	*
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |                                Float64                                |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* |              Uint32               |               Uint32              |
	* |-------- -------- -------- -------- -------- -------- -------- --------|
	* ```
	*
	* If little endian (more significant bits last):
	*
	* ```text
	*                         <-- lower      higher -->
	* |   f7       f6       f5       f4       f3       f2    e2 | f1 |s|  e1  |
	* ```
	*
	* If big endian (more significant bits first):
	*
	* ```text
	*                         <-- higher      lower -->
	* |s| e1    e2 | f1     f2       f3       f4       f5        f6      f7   |
	* ```
	*
	* In which Uint32 can we find the higher order bits? If little endian, the second; if big endian, the first.
	*
	* ## References
	*
	* -   [Open Group][1]
	*
	* [1]: http://pubs.opengroup.org/onlinepubs/9629399/chap14.htm
	*
	* @param {number} x - double
	* @param {uinteger32} high - unsigned 32-bit integer to replace the higher order word of `x`
	* @returns {number} double having the same lower order word as `x`
	*
	* @example
	* var high = 5 >>> 0; // => 0 00000000000 00000000000000000101
	*
	* var y = setHighWord( 3.14e201, high ); // => 0 00000000000 0000000000000000010110010011110010110101100010000010
	* // returns 1.18350528745e-313
	*
	* @example
	* var PINF = require( '@stdlib/constants-float64-pinf' ); // => 0 11111111111 00000000000000000000 00000000000000000000000000000000
	*
	* var high = 1072693248 >>> 0; // => 0 01111111111 00000000000000000000
	*
	* // Set the higher order bits of `+infinity` to return `1`:
	* var y = setHighWord( PINF, high ); // => 0 01111111111 0000000000000000000000000000000000000000000000000000
	* // returns 1.0
	*/
	function setHighWord( x, high ) {
		FLOAT64_VIEW[ 0 ] = x;
		UINT32_VIEW[ HIGH ] = ( high >>> 0 ); // identity bit shift to ensure integer
		return FLOAT64_VIEW[ 0 ];
	}


	// EXPORTS //

	main$u = setHighWord;
	return main$u;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$A;
var hasRequiredLib$A;

function requireLib$A () {
	if (hasRequiredLib$A) return lib$A;
	hasRequiredLib$A = 1;

	/**
	* Set the more significant 32 bits of a double-precision floating-point number.
	*
	* @module @stdlib/number-float64-base-set-high-word
	*
	* @example
	* var setHighWord = require( '@stdlib/number-float64-base-set-high-word' );
	*
	* var high = 5 >>> 0; // => 0 00000000000 00000000000000000101
	*
	* var y = setHighWord( 3.14e201, high ); // => 0 00000000000 0000000000000000010110010011110010110101100010000010
	* // returns 1.18350528745e-313
	*
	* @example
	* var PINF = require( '@stdlib/constants-float64-pinf' ); // => 0 11111111111 00000000000000000000 00000000000000000000000000000000
	* var setHighWord = require( '@stdlib/number-float64-base-set-high-word' );
	*
	* var high = 1072693248 >>> 0; // => 0 01111111111 00000000000000000000
	*
	* // Set the higher order bits of `+infinity` to return `1`:
	* var y = setHighWord( PINF, high ); // => 0 01111111111 0000000000000000000000000000000000000000000000000000
	* // returns 1.0
	*/

	// MODULES //

	var main = requireMain$u();


	// EXPORTS //

	lib$A = main;
	return lib$A;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_p$2;
var hasRequiredPolyval_p$2;

function requirePolyval_p$2 () {
	if (hasRequiredPolyval_p$2) return polyval_p$2;
	hasRequiredPolyval_p$2 = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.3999999999940942;
		}
		return 0.3999999999940942 + (x * (0.22222198432149784 + (x * 0.15313837699209373))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_p$2 = evalpoly;
	return polyval_p$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_q;
var hasRequiredPolyval_q;

function requirePolyval_q () {
	if (hasRequiredPolyval_q) return polyval_q;
	hasRequiredPolyval_q = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.6666666666666735;
		}
		return 0.6666666666666735 + (x * (0.2857142874366239 + (x * (0.1818357216161805 + (x * 0.14798198605116586))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_q = evalpoly;
	return polyval_q;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/e_log.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 1993 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var main$t;
var hasRequiredMain$t;

function requireMain$t () {
	if (hasRequiredMain$t) return main$t;
	hasRequiredMain$t = 1;

	// MODULES //

	var getHighWord = requireLib$U();
	var setHighWord = requireLib$A();
	var isnan = requireLib$1s();
	var BIAS = requireLib$1l();
	var NINF = requireLib$1n();
	var polyvalP = requirePolyval_p$2();
	var polyvalQ = requirePolyval_q();


	// VARIABLES //

	var LN2_HI = 6.93147180369123816490e-01; // 3FE62E42 FEE00000
	var LN2_LO = 1.90821492927058770002e-10; // 3DEA39EF 35793C76
	var TWO54 = 1.80143985094819840000e+16;  // 0x43500000, 0x00000000
	var ONE_THIRD = 0.33333333333333333;

	// 0x000fffff = 1048575 => 0 00000000000 11111111111111111111
	var HIGH_SIGNIFICAND_MASK = 0x000fffff|0; // asm type annotation

	// 0x7ff00000 = 2146435072 => 0 11111111111 00000000000000000000 => biased exponent: 2047 = 1023+1023 => 2^1023
	var HIGH_MAX_NORMAL_EXP = 0x7ff00000|0; // asm type annotation

	// 0x00100000 = 1048576 => 0 00000000001 00000000000000000000 => biased exponent: 1 = -1022+1023 => 2^-1022
	var HIGH_MIN_NORMAL_EXP = 0x00100000|0; // asm type annotation

	// 0x3ff00000 = 1072693248 => 0 01111111111 00000000000000000000 => biased exponent: 1023 = 0+1023 => 2^0 = 1
	var HIGH_BIASED_EXP_0 = 0x3ff00000|0; // asm type annotation


	// MAIN //

	/**
	* Evaluates the natural logarithm of a double-precision floating-point number.
	*
	* @param {NonNegativeNumber} x - input value
	* @returns {number} function value
	*
	* @example
	* var v = ln( 4.0 );
	* // returns ~1.386
	*
	* @example
	* var v = ln( 0.0 );
	* // returns -Infinity
	*
	* @example
	* var v = ln( Infinity );
	* // returns Infinity
	*
	* @example
	* var v = ln( NaN );
	* // returns NaN
	*
	* @example
	* var v = ln( -4.0 );
	* // returns NaN
	*/
	function ln( x ) {
		var hfsq;
		var hx;
		var t2;
		var t1;
		var k;
		var R;
		var f;
		var i;
		var j;
		var s;
		var w;
		var z;

		if ( x === 0.0 ) {
			return NINF;
		}
		if ( isnan( x ) || x < 0.0 ) {
			return NaN;
		}
		hx = getHighWord( x );
		k = 0|0; // asm type annotation
		if ( hx < HIGH_MIN_NORMAL_EXP ) {
			// Case: 0 < x < 2**-1022
			k -= 54|0; // asm type annotation

			// Subnormal number, scale up `x`:
			x *= TWO54;
			hx = getHighWord( x );
		}
		if ( hx >= HIGH_MAX_NORMAL_EXP ) {
			return x + x;
		}
		k += ( ( hx>>20 ) - BIAS )|0; // asm type annotation
		hx &= HIGH_SIGNIFICAND_MASK;
		i = ( (hx+0x95f64) & 0x100000 )|0; // asm type annotation

		// Normalize `x` or `x/2`...
		x = setHighWord( x, hx|(i^HIGH_BIASED_EXP_0) );
		k += ( i>>20 )|0; // asm type annotation
		f = x - 1.0;
		if ( (HIGH_SIGNIFICAND_MASK&(2+hx)) < 3 ) {
			// Case: -2**-20 <= f < 2**-20
			if ( f === 0.0 ) {
				if ( k === 0 ) {
					return 0.0;
				}
				return (k * LN2_HI) + (k * LN2_LO);
			}
			R = f * f * ( 0.5 - (ONE_THIRD*f) );
			if ( k === 0 ) {
				return f - R;
			}
			return (k * LN2_HI) - ( (R-(k*LN2_LO)) - f );
		}
		s = f / (2.0 + f);
		z = s * s;
		i = ( hx - 0x6147a )|0; // asm type annotation
		w = z * z;
		j = ( 0x6b851 - hx )|0; // asm type annotation
		t1 = w * polyvalP( w );
		t2 = z * polyvalQ( w );
		i |= j;
		R = t2 + t1;
		if ( i > 0 ) {
			hfsq = 0.5 * f * f;
			if ( k === 0 ) {
				return f - ( hfsq - (s * (hfsq+R)) );
			}
			return (k * LN2_HI) - ( hfsq - ((s*(hfsq+R))+(k*LN2_LO)) - f );
		}
		if ( k === 0 ) {
			return f - (s*(f-R));
		}
		return (k * LN2_HI) - ( ( (s*(f-R)) - (k*LN2_LO) ) - f );
	}


	// EXPORTS //

	main$t = ln;
	return main$t;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$z;
var hasRequiredLib$z;

function requireLib$z () {
	if (hasRequiredLib$z) return lib$z;
	hasRequiredLib$z = 1;

	/**
	* Evaluate the natural logarithm of a double-precision floating-point number.
	*
	* @module @stdlib/math-base-special-ln
	*
	* @example
	* var ln = require( '@stdlib/math-base-special-ln' );
	*
	* var v = ln( 4.0 );
	* // returns ~1.386
	*
	* v = ln( 0.0 );
	* // returns -Infinity
	*
	* v = ln( Infinity );
	* // returns Infinity
	*
	* v = ln( NaN );
	* // returns NaN
	*
	* v = ln( -4.0 );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$t();


	// EXPORTS //

	lib$z = main;
	return lib$z;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$y;
var hasRequiredLib$y;

function requireLib$y () {
	if (hasRequiredLib$y) return lib$y;
	hasRequiredLib$y = 1;

	/**
	* The mathematical constant `π` times `2`.
	*
	* @module @stdlib/constants-float64-two-pi
	* @type {number}
	*
	* @example
	* var TWO_PI = require( '@stdlib/constants-float64-two-pi' );
	* // returns 6.283185307179586
	*/


	// MAIN //

	/**
	* The mathematical constant `π` times `2`.
	*
	* @constant
	* @type {number}
	* @default 6.283185307179586
	* @see [Wikipedia]{@link https://en.wikipedia.org/wiki/Pi}
	*/
	var TWO_PI = 6.28318530717958647692528676655900576839433879875021164194988918461563281257241799725606965068423413596429617303; // eslint-disable-line max-len


	// EXPORTS //

	lib$y = TWO_PI;
	return lib$y;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$x;
var hasRequiredLib$x;

function requireLib$x () {
	if (hasRequiredLib$x) return lib$x;
	hasRequiredLib$x = 1;

	/**
	* Euler's number.
	*
	* @module @stdlib/constants-float64-e
	* @type {number}
	*
	* @example
	* var E = require( '@stdlib/constants-float64-e' );
	* // returns 2.718281828459045
	*/


	// MAIN //

	/**
	* Euler's number.
	*
	* @constant
	* @type {number}
	* @default 2.718281828459045
	* @see [OEIS]{@link https://oeis.org/A001113}
	* @see [Wikipedia]{@link https://en.wikipedia.org/wiki/E_(mathematical_constant)}
	*/
	var E = 2.718281828459045235360287471352662497757247093699959574966;


	// EXPORTS //

	lib$x = E;
	return lib$x;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$s;
var hasRequiredMain$s;

function requireMain$s () {
	if (hasRequiredMain$s) return main$s;
	hasRequiredMain$s = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var ln = requireLib$z();
	var TWO_PI = requireLib$y();
	var E = requireLib$x();


	// MAIN //

	/**
	* Returns the differential entropy for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} entropy
	*
	* @example
	* var y = entropy( 0.0, 1.0 );
	* // returns ~1.419
	*
	* @example
	* var y = entropy( 5.0, 3.0 );
	* // returns ~2.518
	*
	* @example
	* var y = entropy( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = entropy( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = entropy( 0.0, 0.0 );
	* // returns NaN
	*/
	function entropy( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return 0.5 * ln( TWO_PI * E * sigma*sigma );
	}


	// EXPORTS //

	main$s = entropy;
	return main$s;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$w;
var hasRequiredLib$w;

function requireLib$w () {
	if (hasRequiredLib$w) return lib$w;
	hasRequiredLib$w = 1;

	/**
	* Normal distribution differential entropy.
	*
	* @module @stdlib/stats-base-dists-normal-entropy
	*
	* @example
	* var entropy = require( '@stdlib/stats-base-dists-normal-entropy' );
	*
	* var y = entropy( 0.0, 1.0 );
	* // returns ~1.419
	*
	* y = entropy( 4.0, 3.0 );
	* // returns ~2.518
	*/

	// MODULES //

	var main = requireMain$s();


	// EXPORTS //

	lib$w = main;
	return lib$w;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$r;
var hasRequiredMain$r;

function requireMain$r () {
	if (hasRequiredMain$r) return main$r;
	hasRequiredMain$r = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the excess kurtosis for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} excess kurtosis
	*
	* @example
	* var y = kurtosis( 0.0, 1.0 );
	* // returns 0.0
	*
	* @example
	* var y = kurtosis( 5.0, 3.0 );
	* // returns 0.0
	*
	* @example
	* var y = kurtosis( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = kurtosis( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = kurtosis( 0.0, 0.0 );
	* // returns NaN
	*/
	function kurtosis( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return 0.0;
	}


	// EXPORTS //

	main$r = kurtosis;
	return main$r;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$v;
var hasRequiredLib$v;

function requireLib$v () {
	if (hasRequiredLib$v) return lib$v;
	hasRequiredLib$v = 1;

	/**
	* Normal distribution kurtosis.
	*
	* @module @stdlib/stats-base-dists-normal-kurtosis
	*
	* @example
	* var kurtosis = require( '@stdlib/stats-base-dists-normal-kurtosis' );
	*
	* var y = kurtosis( 0.0, 1.0 );
	* // returns 0.0
	*
	* y = kurtosis( 4.0, 3.0 );
	* // returns 0.0
	*/

	// MODULES //

	var main = requireMain$r();


	// EXPORTS //

	lib$v = main;
	return lib$v;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$q;
var hasRequiredMain$q;

function requireMain$q () {
	if (hasRequiredMain$q) return main$q;
	hasRequiredMain$q = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the expected value for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} expected value
	*
	* @example
	* var y = mean( 0.0, 1.0 );
	* // returns 0.0
	*
	* @example
	* var y = mean( 5.0, 2.0 );
	* // returns 5.0
	*
	* @example
	* var y = mean( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = mean( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = mean( 0.0, 0.0 );
	* // returns NaN
	*/
	function mean( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return mu;
	}


	// EXPORTS //

	main$q = mean;
	return main$q;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$u;
var hasRequiredLib$u;

function requireLib$u () {
	if (hasRequiredLib$u) return lib$u;
	hasRequiredLib$u = 1;

	/**
	* Normal distribution expected value.
	*
	* @module @stdlib/stats-base-dists-normal-mean
	*
	* @example
	* var mean = require( '@stdlib/stats-base-dists-normal-mean' );
	*
	* var y = mean( 0.0, 1.0 );
	* // returns 0.0
	*
	* y = mean( 4.0, 2.0 );
	* // returns 4.0
	*/

	// MODULES //

	var mean = requireMain$q();


	// EXPORTS //

	lib$u = mean;
	return lib$u;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$p;
var hasRequiredMain$p;

function requireMain$p () {
	if (hasRequiredMain$p) return main$p;
	hasRequiredMain$p = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the median for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} median
	*
	* @example
	* var y = median( 0.0, 1.0 );
	* // returns 0.0
	*
	* @example
	* var y = median( 5.0, 2.0 );
	* // returns 5.0
	*
	* @example
	* var y = median( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = median( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = median( 0.0, 0.0 );
	* // returns NaN
	*/
	function median( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return mu;
	}


	// EXPORTS //

	main$p = median;
	return main$p;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$t;
var hasRequiredLib$t;

function requireLib$t () {
	if (hasRequiredLib$t) return lib$t;
	hasRequiredLib$t = 1;

	/**
	* Normal distribution median.
	*
	* @module @stdlib/stats-base-dists-normal-median
	*
	* @example
	* var median = require( '@stdlib/stats-base-dists-normal-median' );
	*
	* var y = median( 0.0, 1.0 );
	* // returns 0.0
	*
	* y = median( 4.0, 2.0 );
	* // returns 4.0
	*/

	// MODULES //

	var median = requireMain$p();


	// EXPORTS //

	lib$t = median;
	return lib$t;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$o;
var hasRequiredMain$o;

function requireMain$o () {
	if (hasRequiredMain$o) return main$o;
	hasRequiredMain$o = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the mode for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} mode
	*
	* @example
	* var y = mode( 0.0, 1.0 );
	* // returns 0.0
	*
	* @example
	* var y = mode( 5.0, 2.0 );
	* // returns 5.0
	*
	* @example
	* var y = mode( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = mode( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = mode( 0.0, 0.0 );
	* // returns NaN
	*/
	function mode( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return mu;
	}


	// EXPORTS //

	main$o = mode;
	return main$o;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$s;
var hasRequiredLib$s;

function requireLib$s () {
	if (hasRequiredLib$s) return lib$s;
	hasRequiredLib$s = 1;

	/**
	* Normal distribution mode.
	*
	* @module @stdlib/stats-base-dists-normal-mode
	*
	* @example
	* var mode = require( '@stdlib/stats-base-dists-normal-mode' );
	*
	* var y = mode( 0.0, 1.0 );
	* // returns 0.0
	*
	* y = mode( 4.0, 2.0 );
	* // returns 4.0
	*/

	// MODULES //

	var mode = requireMain$o();


	// EXPORTS //

	lib$s = mode;
	return lib$s;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$n;
var hasRequiredMain$n;

function requireMain$n () {
	if (hasRequiredMain$n) return main$n;
	hasRequiredMain$n = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the skewness for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} skewness
	*
	* @example
	* var y = skewness( 0.0, 1.0 );
	* // returns 0.0
	*
	* @example
	* var y = skewness( 5.0, 3.0 );
	* // returns 0.0
	*
	* @example
	* var y = skewness( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = skewness( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = skewness( 0.0, 0.0 );
	* // returns NaN
	*/
	function skewness( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return 0.0;
	}


	// EXPORTS //

	main$n = skewness;
	return main$n;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$r;
var hasRequiredLib$r;

function requireLib$r () {
	if (hasRequiredLib$r) return lib$r;
	hasRequiredLib$r = 1;

	/**
	* Normal distribution skewness.
	*
	* @module @stdlib/stats-base-dists-normal-skewness
	*
	* @example
	* var skewness = require( '@stdlib/stats-base-dists-normal-skewness' );
	*
	* var y = skewness( 0.0, 1.0 );
	* // returns 0.0
	*
	* y = skewness( 4.0, 3.0 );
	* // returns 0.0
	*/

	// MODULES //

	var main = requireMain$n();


	// EXPORTS //

	lib$r = main;
	return lib$r;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$m;
var hasRequiredMain$m;

function requireMain$m () {
	if (hasRequiredMain$m) return main$m;
	hasRequiredMain$m = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the standard deviation for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {PositiveNumber} standard deviation
	*
	* @example
	* var y = stdev( 0.0, 1.0 );
	* // returns 1.0
	*
	* @example
	* var y = stdev( 5.0, 3.0 );
	* // returns 3.0
	*
	* @example
	* var y = stdev( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = stdev( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = stdev( 0.0, 0.0 );
	* // returns NaN
	*/
	function stdev( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return sigma;
	}


	// EXPORTS //

	main$m = stdev;
	return main$m;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$q;
var hasRequiredLib$q;

function requireLib$q () {
	if (hasRequiredLib$q) return lib$q;
	hasRequiredLib$q = 1;

	/**
	* Normal distribution standard deviation.
	*
	* @module @stdlib/stats-base-dists-normal-stdev
	*
	* @example
	* var stdev = require( '@stdlib/stats-base-dists-normal-stdev' );
	*
	* var y = stdev( 0.0, 1.0 );
	* // returns 1.0
	*
	* y = stdev( 4.0, 3.0 );
	* // returns 3.0
	*/

	// MODULES //

	var main = requireMain$m();


	// EXPORTS //

	lib$q = main;
	return lib$q;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$l;
var hasRequiredMain$l;

function requireMain$l () {
	if (hasRequiredMain$l) return main$l;
	hasRequiredMain$l = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns the variance for a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {PositiveNumber} variance
	*
	* @example
	* var y = variance( 0.0, 1.0 );
	* // returns 1.0
	*
	* @example
	* var y = variance( 5.0, 3.0 );
	* // returns 9.0
	*
	* @example
	* var y = variance( NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = variance( 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = variance( 0.0, 0.0 );
	* // returns NaN
	*/
	function variance( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return sigma*sigma;
	}


	// EXPORTS //

	main$l = variance;
	return main$l;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$p;
var hasRequiredLib$p;

function requireLib$p () {
	if (hasRequiredLib$p) return lib$p;
	hasRequiredLib$p = 1;

	/**
	* Normal distribution variance.
	*
	* @module @stdlib/stats-base-dists-normal-variance
	*
	* @example
	* var variance = require( '@stdlib/stats-base-dists-normal-variance' );
	*
	* var y = variance( 0.0, 1.0 );
	* // returns 1.0
	*
	* y = variance( 4.0, 3.0 );
	* // returns 9.0
	*/

	// MODULES //

	var main = requireMain$l();


	// EXPORTS //

	lib$p = main;
	return lib$p;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_lp;
var hasRequiredPolyval_lp;

function requirePolyval_lp () {
	if (hasRequiredPolyval_lp) return polyval_lp;
	hasRequiredPolyval_lp = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.6666666666666735;
		}
		return 0.6666666666666735 + (x * (0.3999999999940942 + (x * (0.2857142874366239 + (x * (0.22222198432149784 + (x * (0.1818357216161805 + (x * (0.15313837699209373 + (x * 0.14798198605116586))))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_lp = evalpoly;
	return polyval_lp;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright, license, and long comment were part of the original implementation available as part of [FDLIBM]{@link http://www.netlib.org/fdlibm/s_log1p.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 1993 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var main$k;
var hasRequiredMain$k;

function requireMain$k () {
	if (hasRequiredMain$k) return main$k;
	hasRequiredMain$k = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var getHighWord = requireLib$U();
	var setHighWord = requireLib$A();
	var PINF = requireLib$1m();
	var NINF = requireLib$1n();
	var FLOAT64_EXPONENT_BIAS = requireLib$1l();
	var polyval = requirePolyval_lp();


	// VARIABLES //

	// High and low words of ln(2):
	var LN2_HI = 6.93147180369123816490e-01; // 0x3fe62e42 0xfee00000
	var LN2_LO = 1.90821492927058770002e-10; // 0x3dea39ef 0x35793c76

	// sqrt(2)-1:
	var SQRT2M1 = 4.142135623730950488017e-01; // 0x3fda8279 0x99fcef34

	// sqrt(2)/2-1:
	var SQRT2HALFM1 = -2.928932188134524755992e-01; // 0xbfd2bec3 0x33018866

	// 2**-29:
	var SMALL = 1.862645149230957e-09; // 0x3e200000 0x00000000

	// 2**-54:
	var TINY = 5.551115123125783e-17;

	// Max integer (unsafe) => 2**53:
	var TWO53 = 9007199254740992;

	// 2/3:
	var TWO_THIRDS = 6.666666666666666666e-01;


	// MAIN //

	/**
	* Evaluates the natural logarithm of \\(1+x\\).
	*
	* ## Method
	*
	* 1.  Argument Reduction: find \\(k\\) and \\(f\\) such that
	*
	*     ```tex
	*     1+x = 2^k (1+f)
	*     ```
	*
	*     where
	*
	*     ```tex
	*     \frac{\sqrt{2}}{2} < 1+f < \sqrt{2}
	*     ```
	*
	*     <!-- <note> -->
	*
	*     If \\(k=0\\), then \\(f=x\\) is exact. However, if \\(k \neq 0\\), then \\(f\\) may not be representable exactly. In that case, a correction term is needed. Let
	*
	*     ```tex
	*     u = \operatorname{round}(1+x)
	*     ```
	*
	*     and
	*
	*     ```tex
	*     c = (1+x) - u
	*     ```
	*
	*     then
	*
	*     ```tex
	*     \ln (1+x) - \ln u \approx \frac{c}{u}
	*     ```
	*
	*     We can thus proceed to compute \\(\ln(u)\\), and add back the correction term \\(c/u\\).
	*
	*     <!-- </note> -->
	*
	*     <!-- <note> -->
	*
	*     When \\(x > 2^{53}\\), one can simply return \\(\ln(x)\\).
	*
	*     <!-- </note> -->
	*
	* 2.  Approximation of \\(\operatorname{log1p}(f)\\). Let
	*
	*     ```tex
	*     s = \frac{f}{2+f}
	*     ```
	*
	*     based on
	*
	*     ```tex
	*     \begin{align*}
	*     \ln 1+f &= \ln (1+s) - \ln (1-s) \\
	*             &= 2s + \frac{2}{3} s^3 + \frac{2}{5} s^5 + ... \\
	*             &= 2s + sR \\
	*     \end{align*}
	*     ```
	*
	*     We use a special Reme algorithm on \\(\[0,0.1716\]\\) to generate a polynomial of degree \\(14\\) to approximate \\(R\\). The maximum error of this polynomial approximation is bounded by \\(2^{-58.45}\\). In other words,
	*
	*     ```tex
	*     R(z) \approx \mathrm{Lp}_1 s^2 + \mathrm{Lp}_2 s^4 + \mathrm{Lp}_3 s^6 + \mathrm{Lp}_4 s^8 + \mathrm{Lp}_5 s^{10} + \mathrm{Lp}_6 s^{12} + \mathrm{Lp}_7 s^{14}
	*     ```
	*
	*     and
	*
	*     ```tex
	*     | \mathrm{Lp}_1 s^2 + \ldots + \mathrm{Lp}_7 s^14 - R(z) | \leq 2^{-58.45}
	*     ```
	*
	*     <!-- <note> -->
	*
	*     The values of \\(Lp1\\) to \\(Lp7\\) may be found in the source.
	*
	*     <!-- </note> -->
	*
	*     Note that
	*
	*     ```tex
	*     \begin{align*}
	*     2s &= f - sf \\
	*        &= f - \frac{f^2}{2} + s \frac{f^2}{2} \\
	*     \end{align*}
	*     ```
	*
	*     In order to guarantee error in \\(\ln\\) below \\(1\ \mathrm{ulp}\\), we compute the log by
	*
	*     ```tex
	*     \operatorname{log1p}(f) = f - \biggl(\frac{f^2}{2} - s\biggl(\frac{f^2}{2}+R\biggr)\biggr)
	*     ```
	*
	* 3.  Finally,
	*
	*     ```tex
	*     \begin{align*}
	*     \operatorname{log1p}(x) &= k \cdot \mathrm{ln2} + \operatorname{log1p}(f) \\
	*     &= k \cdot \mathrm{ln2}_{hi}+\biggl(f-\biggl(\frac{f^2}{2}-\biggl(s\biggl(\frac{f^2}{2}+R\biggr)+k \cdot \mathrm{ln2}_{lo}\biggr)\biggr)\biggr) \\
	*     \end{align*}
	*     ```
	*
	*     Here \\(\mathrm{ln2}\\) is split into two floating point numbers:
	*
	*     ```tex
	*     \mathrm{ln2}_{hi} + \mathrm{ln2}_{lo}
	*     ```
	*
	*     where \\(n \cdot \mathrm{ln2}_{hi}\\) is always exact for \\(|n| < 2000\\).
	*
	* ## Special Cases
	*
	* -   \\(\operatorname{log1p}(x) = \mathrm{NaN}\\) with signal if \\(x < -1\\) (including \\(-\infty\\))
	* -   \\(\operatorname{log1p}(+\infty) = +\infty\\)
	* -   \\(\operatorname{log1p}(-1) = -\infty\\) with signal
	* -   \\(\operatorname{log1p}(\mathrm{NaN})= \mathrm{NaN}\\) with no signal
	*
	* ## Notes
	*
	* -   According to an error analysis, the error is always less than \\(1\\) ulp (unit in the last place).
	*
	* -   The hexadecimal values are the intended ones for the used constants. The decimal values may be used, provided that the compiler will convert from decimal to binary accurately enough to produce the hexadecimal values shown.
	*
	* -   Assuming \\(\ln(x)\\) is accurate, the following algorithm can be used to evaluate \\(\operatorname{log1p}(x)\\) to within a few ULP:
	*
	*     ```javascript
	*     var u = 1.0 + x;
	*     if ( u === 1.0 ) {
	*         return x;
	*     } else {
	*         return ln(u) * (x/(u-1.0));
	*     }
	*     ```
	*
	*     See HP-15C Advanced Functions Handbook, p.193.
	*
	* @param {number} x - input value
	* @returns {number} the natural logarithm of `1+x`
	*
	* @example
	* var v = log1p( 4.0 );
	* // returns ~1.609
	*
	* @example
	* var v = log1p( -1.0 );
	* // returns -Infinity
	*
	* @example
	* var v = log1p( 0.0 );
	* // returns 0.0
	*
	* @example
	* var v = log1p( -0.0 );
	* // returns -0.0
	*
	* @example
	* var v = log1p( -2.0 );
	* // returns NaN
	*
	* @example
	* var v = log1p( NaN );
	* // returns NaN
	*/
	function log1p( x ) {
		var hfsq;
		var hu;
		var y;
		var f;
		var c;
		var s;
		var z;
		var R;
		var u;
		var k;

		if ( x < -1.0 || isnan( x ) ) {
			return NaN;
		}
		if ( x === -1.0 ) {
			return NINF;
		}
		if ( x === PINF ) {
			return x;
		}
		if ( x === 0.0 ) {
			return x; // handle +-0 (IEEE 754-2008 spec)
		}
		// Set y = |x|:
		if ( x < 0.0 ) {
			y = -x;
		} else {
			y = x;
		}
		// Argument reduction...
		k = 1;

		// Check if argument reduction is needed and if we can just return a small value approximation requiring less computation but with equivalent accuracy...
		if ( y < SQRT2M1 ) { // if |x| < sqrt(2)-1 => ~0.41422
			if ( y < SMALL ) { // if |x| < 2**-29
				if ( y < TINY ) { // if |x| < 2**-54
					return x;
				}
				// Use a simple two-term Taylor series...
				return x - ( x*x*0.5 );
			}
			// Check if `f=x` can be represented exactly (no need for correction terms), allowing us to bypass argument reduction...
			if ( x > SQRT2HALFM1 ) { // if x > sqrt(2)/2-1 => ~-0.2929
				// -0.2929 < x < 0.41422
				k = 0;
				f = x; // exact
				hu = 1;
			}
		}
		// Address case where `f` cannot be represented exactly...
		if ( k !== 0 ) {
			if ( y < TWO53 ) {
				u = 1.0 + x;
				hu = getHighWord( u );

				// Bit shift to isolate the exponent and then subtract the bias:
				k = (hu>>20) - FLOAT64_EXPONENT_BIAS;

				// Correction term...
				if ( k > 0 ) { // positive unbiased exponent
					c = 1.0 - (u-x);
				} else { // nonpositive unbiased exponent
					c = x - (u-1.0);
				}
				c /= u;
			} else {
				u = x;
				hu = getHighWord( u );

				// Bit shift to isolate the exponent and then subtract the bias:
				k = (hu>>20) - FLOAT64_EXPONENT_BIAS;

				// Correction term is zero:
				c = 0;
			}
			// Apply a bit mask (0 00000000000 11111111111111111111) to remove the exponent:
			hu &= 0x000fffff; // max value => 1048575

			// Check if u significand is less than sqrt(2) significand => 0x6a09e => 01101010000010011110
			if ( hu < 434334 ) {
				// Normalize u by setting the exponent to 1023 (bias) => 0x3ff00000 => 0 01111111111 00000000000000000000
				u = setHighWord( u, hu|0x3ff00000 );
			} else {
				k += 1;

				// Normalize u/2 by setting the exponent to 1022 (bias-1 => 2**-1 = 1/2) => 0x3fe00000 => 0 01111111110 00000000000000000000
				u = setHighWord( u, hu|0x3fe00000 );

				// Subtract hu significand from next largest hu => 0 00000000001 00000000000000000000 => 0x00100000 => 1048576
				hu = (1048576-hu)>>2;
			}
			f = u - 1.0;
		}
		// Approximation of log1p(f)...
		hfsq = 0.5 * f * f;
		if ( hu === 0 ) { // if |f| < 2**-20
			if ( f === 0.0 ) {
				c += k * LN2_LO;
				return ( k * LN2_HI ) + c;
			}
			R = hfsq * (1.0 - ( TWO_THIRDS*f ) ); // avoid division
			return ( k*LN2_HI ) - ( (R - ( (k*LN2_LO) + c)) - f );
		}
		s = f / (2.0 + f);
		z = s * s;

		R = z * polyval( z );

		if ( k === 0 ) {
			return f - ( hfsq - ( s*(hfsq+R) ) );
		}
		return ( k*LN2_HI ) - ( (hfsq - ( (s*(hfsq+R)) + ((k*LN2_LO) + c))) - f );
	}


	// EXPORTS //

	main$k = log1p;
	return main$k;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$o;
var hasRequiredLib$o;

function requireLib$o () {
	if (hasRequiredLib$o) return lib$o;
	hasRequiredLib$o = 1;

	/**
	* Evaluate the natural logarithm of \\(1+x\\).
	*
	* @module @stdlib/math-base-special-log1p
	*
	* @example
	* var log1p = require( '@stdlib/math-base-special-log1p' );
	*
	* var v = log1p( 4.0 );
	* // returns ~1.609
	*
	* v = log1p( -1.0 );
	* // returns -Infinity
	*
	* v = log1p( 0.0 );
	* // returns 0.0
	*
	* v = log1p( -0.0 );
	* // returns -0.0
	*
	* v = log1p( -2.0 );
	* // returns NaN
	*
	* v = log1p( NaN );
	* // returns NaN
	*/

	// MODULES //

	var log1p = requireMain$k();


	// EXPORTS //

	lib$o = log1p;
	return lib$o;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$j;
var hasRequiredMain$j;

function requireMain$j () {
	if (hasRequiredMain$j) return main$j;
	hasRequiredMain$j = 1;

	/**
	* Computes the squared absolute value of a double-precision floating-point number `x`.
	*
	* @param {number} x - input value
	* @returns {number} squared absolute value
	*
	* @example
	* var v = abs2( -1.0 );
	* // returns 1.0
	*
	* @example
	* var v = abs2( 2.0 );
	* // returns 4.0
	*
	* @example
	* var v = abs2( 0.0 );
	* // returns 0.0
	*
	* @example
	* var v = abs2( -0.0 );
	* // returns 0.0
	*
	* @example
	* var v = abs2( NaN );
	* // returns NaN
	*/
	function abs2( x ) {
		return x * x;
	}


	// EXPORTS //

	main$j = abs2;
	return main$j;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$n;
var hasRequiredLib$n;

function requireLib$n () {
	if (hasRequiredLib$n) return lib$n;
	hasRequiredLib$n = 1;

	/**
	* Compute the squared absolute value of a double-precision floating-point number.
	*
	* @module @stdlib/math-base-special-abs2
	*
	* @example
	* var abs2 = require( '@stdlib/math-base-special-abs2' );
	*
	* var v = abs2( -1.0 );
	* // returns 1.0
	*
	* v = abs2( 2.0 );
	* // returns 4.0
	*
	* v = abs2( 0.0 );
	* // returns 0.0
	*
	* v = abs2( -0.0 );
	* // returns 0.0
	*
	* v = abs2( NaN );
	* // returns NaN
	*/

	// MODULES //

	var abs2 = requireMain$j();


	// EXPORTS //

	lib$n = abs2;
	return lib$n;
}

/* eslint-disable max-len, no-mixed-operators, max-lines */

var erfcx_y100;
var hasRequiredErfcx_y100;

function requireErfcx_y100 () {
	if (hasRequiredErfcx_y100) return erfcx_y100;
	hasRequiredErfcx_y100 = 1;

	// MODULES //

	var floor = requireLib$1r();


	// VARIABLES //

	var table = [ p0, p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12, p13, p14, p15, p16, p17, p18, p19, p20, p21, p22, p23, p24, p25, p26, p27, p28, p29, p30, p31, p32, p33, p34, p35, p36, p37, p38, p39, p40, p41, p42, p43, p44, p45, p46, p47, p48, p49, p50, p51, p52, p53, p54, p55, p56, p57, p58, p59, p60, p61, p62, p63, p64, p65, p66, p67, p68, p69, p70, p71, p72, p73, p74, p75, p76, p77, p78, p79, p80, p81, p82, p83, p84, p85, p86, p87, p88, p89, p90, p91, p92, p93, p94, p95, p96, p97, p98, p99, p100 ];


	// FUNCTIONS //

	/**
	* Chebyshev polynomial for \[0, 0.01).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p0( t ) {
		return 0.70878032454106438663e-3 + (0.71234091047026302958e-3 + (0.35779077297597742384e-5 + (0.17403143962587937815e-7 + (0.81710660047307788845e-10 + (0.36885022360434957634e-12 + 0.15917038551111111111e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.01, 0.02).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p1( t ) {
		return 0.21479143208285144230e-2 + (0.72686402367379996033e-3 + (0.36843175430938995552e-5 + (0.18071841272149201685e-7 + (0.85496449296040325555e-10 + (0.38852037518534291510e-12 + 0.16868473576888888889e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.02, 0.03).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p2( t ) {
		return 0.36165255935630175090e-2 + (0.74182092323555510862e-3 + (0.37948319957528242260e-5 + (0.18771627021793087350e-7 + (0.89484715122415089123e-10 + (0.40935858517772440862e-12 + 0.17872061464888888889e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.03, 0.04).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p3( t ) {
		return 0.51154983860031979264e-2 + (0.75722840734791660540e-3 + (0.39096425726735703941e-5 + (0.19504168704300468210e-7 + (0.93687503063178993915e-10 + (0.43143925959079664747e-12 + 0.18939926435555555556e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.04, 0.05).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p4( t ) {
		return 0.66457513172673049824e-2 + (0.77310406054447454920e-3 + (0.40289510589399439385e-5 + (0.20271233238288381092e-7 + (0.98117631321709100264e-10 + (0.45484207406017752971e-12 + 0.20076352213333333333e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.05, 0.06).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p5( t ) {
		return 0.82082389970241207883e-2 + (0.78946629611881710721e-3 + (0.41529701552622656574e-5 + (0.21074693344544655714e-7 + (0.10278874108587317989e-9 + (0.47965201390613339638e-12 + 0.21285907413333333333e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.06, 0.07).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p6( t ) {
		return 0.98039537275352193165e-2 + (0.80633440108342840956e-3 + (0.42819241329736982942e-5 + (0.21916534346907168612e-7 + (0.10771535136565470914e-9 + (0.50595972623692822410e-12 + 0.22573462684444444444e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.07, 0.08).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p7( t ) {
		return 0.11433927298290302370e-1 + (0.82372858383196561209e-3 + (0.44160495311765438816e-5 + (0.22798861426211986056e-7 + (0.11291291745879239736e-9 + (0.53386189365816880454e-12 + 0.23944209546666666667e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.08, 0.09).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p8( t ) {
		return 0.13099232878814653979e-1 + (0.84167002467906968214e-3 + (0.45555958988457506002e-5 + (0.23723907357214175198e-7 + (0.11839789326602695603e-9 + (0.56346163067550237877e-12 + 0.25403679644444444444e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.09, 0.10).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p9( t ) {
		return 0.14800987015587535621e-1 + (0.86018092946345943214e-3 + (0.47008265848816866105e-5 + (0.24694040760197315333e-7 + (0.12418779768752299093e-9 + (0.59486890370320261949e-12 + 0.26957764568888888889e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.10, 0.11).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p10( t ) {
		return 0.16540351739394069380e-1 + (0.87928458641241463952e-3 + (0.48520195793001753903e-5 + (0.25711774900881709176e-7 + (0.13030128534230822419e-9 + (0.62820097586874779402e-12 + 0.28612737351111111111e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.11, 0.12).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p11( t ) {
		return 0.18318536789842392647e-1 + (0.89900542647891721692e-3 + (0.50094684089553365810e-5 + (0.26779777074218070482e-7 + (0.13675822186304615566e-9 + (0.66358287745352705725e-12 + 0.30375273884444444444e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.12, 0.13).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p12( t ) {
		return 0.20136801964214276775e-1 + (0.91936908737673676012e-3 + (0.51734830914104276820e-5 + (0.27900878609710432673e-7 + (0.14357976402809042257e-9 + (0.70114790311043728387e-12 + 0.32252476000000000000e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.13, 0.14).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p13( t ) {
		return 0.21996459598282740954e-1 + (0.94040248155366777784e-3 + (0.53443911508041164739e-5 + (0.29078085538049374673e-7 + (0.15078844500329731137e-9 + (0.74103813647499204269e-12 + 0.34251892320000000000e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.14, 0.15).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p14( t ) {
		return 0.23898877187226319502e-1 + (0.96213386835900177540e-3 + (0.55225386998049012752e-5 + (0.30314589961047687059e-7 + (0.15840826497296335264e-9 + (0.78340500472414454395e-12 + 0.36381553564444444445e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.15, 0.16).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p15( t ) {
		return 0.25845480155298518485e-1 + (0.98459293067820123389e-3 + (0.57082915920051843672e-5 + (0.31613782169164830118e-7 + (0.16646478745529630813e-9 + (0.82840985928785407942e-12 + 0.38649975768888888890e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.16, 0.17).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p16( t ) {
		return 0.27837754783474696598e-1 + (0.10078108563256892757e-2 + (0.59020366493792212221e-5 + (0.32979263553246520417e-7 + (0.17498524159268458073e-9 + (0.87622459124842525110e-12 + 0.41066206488888888890e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.17, 0.18).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p17( t ) {
		return 0.29877251304899307550e-1 + (0.10318204245057349310e-2 + (0.61041829697162055093e-5 + (0.34414860359542720579e-7 + (0.18399863072934089607e-9 + (0.92703227366365046533e-12 + 0.43639844053333333334e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.18, 0.19).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p18( t ) {
		return 0.31965587178596443475e-1 + (0.10566560976716574401e-2 + (0.63151633192414586770e-5 + (0.35924638339521924242e-7 + (0.19353584758781174038e-9 + (0.98102783859889264382e-12 + 0.46381060817777777779e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.19, 0.20).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p19( t ) {
		return 0.34104450552588334840e-1 + (0.10823541191350532574e-2 + (0.65354356159553934436e-5 + (0.37512918348533521149e-7 + (0.20362979635817883229e-9 + (0.10384187833037282363e-11 + 0.49300625262222222221e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.20, 0.21).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p20( t ) {
		return 0.36295603928292425716e-1 + (0.11089526167995268200e-2 + (0.67654845095518363577e-5 + (0.39184292949913591646e-7 + (0.21431552202133775150e-9 + (0.10994259106646731797e-11 + 0.52409949102222222221e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.21, 0.22).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p21( t ) {
		return 0.38540888038840509795e-1 + (0.11364917134175420009e-2 + (0.70058230641246312003e-5 + (0.40943644083718586939e-7 + (0.22563034723692881631e-9 + (0.11642841011361992885e-11 + 0.55721092871111111110e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.22, 0.23).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p22( t ) {
		return 0.40842225954785960651e-1 + (0.11650136437945673891e-2 + (0.72569945502343006619e-5 + (0.42796161861855042273e-7 + (0.23761401711005024162e-9 + (0.12332431172381557035e-11 + 0.59246802364444444445e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.23, 0.24).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p23( t ) {
		return 0.43201627431540222422e-1 + (0.11945628793917272199e-2 + (0.75195743532849206263e-5 + (0.44747364553960993492e-7 + (0.25030885216472953674e-9 + (0.13065684400300476484e-11 + 0.63000532853333333334e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.24, 0.25).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p24( t ) {
		return 0.45621193513810471438e-1 + (0.12251862608067529503e-2 + (0.77941720055551920319e-5 + (0.46803119830954460212e-7 + (0.26375990983978426273e-9 + (0.13845421370977119765e-11 + 0.66996477404444444445e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.25, 0.26).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p25( t ) {
		return 0.48103121413299865517e-1 + (0.12569331386432195113e-2 + (0.80814333496367673980e-5 + (0.48969667335682018324e-7 + (0.27801515481905748484e-9 + (0.14674637611609884208e-11 + 0.71249589351111111110e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.26, 0.27).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p26( t ) {
		return 0.50649709676983338501e-1 + (0.12898555233099055810e-2 + (0.83820428414568799654e-5 + (0.51253642652551838659e-7 + (0.29312563849675507232e-9 + (0.15556512782814827846e-11 + 0.75775607822222222221e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.27, 0.28).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p27( t ) {
		return 0.53263363664388864181e-1 + (0.13240082443256975769e-2 + (0.86967260015007658418e-5 + (0.53662102750396795566e-7 + (0.30914568786634796807e-9 + (0.16494420240828493176e-11 + 0.80591079644444444445e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.28, 0.29).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p28( t ) {
		return 0.55946601353500013794e-1 + (0.13594491197408190706e-2 + (0.90262520233016380987e-5 + (0.56202552975056695376e-7 + (0.32613310410503135996e-9 + (0.17491936862246367398e-11 + 0.85713381688888888890e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.29, 0.30).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p29( t ) {
		return 0.58702059496154081813e-1 + (0.13962391363223647892e-2 + (0.93714365487312784270e-5 + (0.58882975670265286526e-7 + (0.34414937110591753387e-9 + (0.18552853109751857859e-11 + 0.91160736711111111110e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.30, 0.31).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p30( t ) {
		return 0.61532500145144778048e-1 + (0.14344426411912015247e-2 + (0.97331446201016809696e-5 + (0.61711860507347175097e-7 + (0.36325987418295300221e-9 + (0.19681183310134518232e-11 + 0.96952238400000000000e-14 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.31, 0.32).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p31( t ) {
		return 0.64440817576653297993e-1 + (0.14741275456383131151e-2 + (0.10112293819576437838e-4 + (0.64698236605933246196e-7 + (0.38353412915303665586e-9 + (0.20881176114385120186e-11 + 0.10310784480000000000e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.32, 0.33).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p32( t ) {
		return 0.67430045633130393282e-1 + (0.15153655418916540370e-2 + (0.10509857606888328667e-4 + (0.67851706529363332855e-7 + (0.40504602194811140006e-9 + (0.22157325110542534469e-11 + 0.10964842115555555556e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.33, 0.34).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p33( t ) {
		return 0.70503365513338850709e-1 + (0.15582323336495709827e-2 + (0.10926868866865231089e-4 + (0.71182482239613507542e-7 + (0.42787405890153386710e-9 + (0.23514379522274416437e-11 + 0.11659571751111111111e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.34, 0.35).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p34( t ) {
		return 0.73664114037944596353e-1 + (0.16028078812438820413e-2 + (0.11364423678778207991e-4 + (0.74701423097423182009e-7 + (0.45210162777476488324e-9 + (0.24957355004088569134e-11 + 0.12397238257777777778e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.35, 0.36).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p35( t ) {
		return 0.76915792420819562379e-1 + (0.16491766623447889354e-2 + (0.11823685320041302169e-4 + (0.78420075993781544386e-7 + (0.47781726956916478925e-9 + (0.26491544403815724749e-11 + 0.13180196462222222222e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.36, 0.37).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p36( t ) {
		return 0.80262075578094612819e-1 + (0.16974279491709504117e-2 + (0.12305888517309891674e-4 + (0.82350717698979042290e-7 + (0.50511496109857113929e-9 + (0.28122528497626897696e-11 + 0.14010889635555555556e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.37, 0.38).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p37( t ) {
		return 0.83706822008980357446e-1 + (0.17476561032212656962e-2 + (0.12812343958540763368e-4 + (0.86506399515036435592e-7 + (0.53409440823869467453e-9 + (0.29856186620887555043e-11 + 0.14891851591111111111e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.38, 0.39).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p38( t ) {
		return 0.87254084284461718231e-1 + (0.17999608886001962327e-2 + (0.13344443080089492218e-4 + (0.90900994316429008631e-7 + (0.56486134972616465316e-9 + (0.31698707080033956934e-11 + 0.15825697795555555556e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.39, 0.40).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p39( t ) {
		return 0.90908120182172748487e-1 + (0.18544478050657699758e-2 + (0.13903663143426120077e-4 + (0.95549246062549906177e-7 + (0.59752787125242054315e-9 + (0.33656597366099099413e-11 + 0.16815130613333333333e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.40, 0.41).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p40( t ) {
		return 0.94673404508075481121e-1 + (0.19112284419887303347e-2 + (0.14491572616545004930e-4 + (0.10046682186333613697e-6 + (0.63221272959791000515e-9 + (0.35736693975589130818e-11 + 0.17862931591111111111e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.41, 0.42).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p41( t ) {
		return 0.98554641648004456555e-1 + (0.19704208544725622126e-2 + (0.15109836875625443935e-4 + (0.10567036667675984067e-6 + (0.66904168640019354565e-9 + (0.37946171850824333014e-11 + 0.18971959040000000000e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.42, 0.43).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p42( t ) {
		return 0.10255677889470089531e0 + (0.20321499629472857418e-2 + (0.15760224242962179564e-4 + (0.11117756071353507391e-6 + (0.70814785110097658502e-9 + (0.40292553276632563925e-11 + 0.20145143075555555556e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.43, 0.44).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p43( t ) {
		return 0.10668502059865093318e0 + (0.20965479776148731610e-2 + (0.16444612377624983565e-4 + (0.11700717962026152749e-6 + (0.74967203250938418991e-9 + (0.42783716186085922176e-11 + 0.21385479360000000000e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.44, 0.45).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p44( t ) {
		return 0.11094484319386444474e0 + (0.21637548491908170841e-2 + (0.17164995035719657111e-4 + (0.12317915750735938089e-6 + (0.79376309831499633734e-9 + (0.45427901763106353914e-11 + 0.22696025653333333333e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.45, 0.46).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p45( t ) {
		return 0.11534201115268804714e0 + (0.22339187474546420375e-2 + (0.17923489217504226813e-4 + (0.12971465288245997681e-6 + (0.84057834180389073587e-9 + (0.48233721206418027227e-11 + 0.24079890062222222222e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.46, 0.47).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p46( t ) {
		return 0.11988259392684094740e0 + (0.23071965691918689601e-2 + (0.18722342718958935446e-4 + (0.13663611754337957520e-6 + (0.89028385488493287005e-9 + (0.51210161569225846701e-11 + 0.25540227111111111111e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.47, 0.48).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p47( t ) {
		return 0.12457298393509812907e0 + (0.23837544771809575380e-2 + (0.19563942105711612475e-4 + (0.14396736847739470782e-6 + (0.94305490646459247016e-9 + (0.54366590583134218096e-11 + 0.27080225920000000000e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.48, 0.49).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p48( t ) {
		return 0.12941991566142438816e0 + (0.24637684719508859484e-2 + (0.20450821127475879816e-4 + (0.15173366280523906622e-6 + (0.99907632506389027739e-9 + (0.57712760311351625221e-11 + 0.28703099555555555556e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.49, 0.50).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p49( t ) {
		return 0.13443048593088696613e0 + (0.25474249981080823877e-2 + (0.21385669591362915223e-4 + (0.15996177579900443030e-6 + (0.10585428844575134013e-8 + (0.61258809536787882989e-11 + 0.30412080142222222222e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.50, 0.51).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p50( t ) {
		return 0.13961217543434561353e0 + (0.26349215871051761416e-2 + (0.22371342712572567744e-4 + (0.16868008199296822247e-6 + (0.11216596910444996246e-8 + (0.65015264753090890662e-11 + 0.32210394506666666666e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.51, 0.52).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p51( t ) {
		return 0.14497287157673800690e0 + (0.27264675383982439814e-2 + (0.23410870961050950197e-4 + (0.17791863939526376477e-6 + (0.11886425714330958106e-8 + (0.68993039665054288034e-11 + 0.34101266222222222221e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.52, 0.53).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p52( t ) {
		return 0.15052089272774618151e0 + (0.28222846410136238008e-2 + (0.24507470422713397006e-4 + (0.18770927679626136909e-6 + (0.12597184587583370712e-8 + (0.73203433049229821618e-11 + 0.36087889048888888890e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.53, 0.54).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p53( t ) {
		return 0.15626501395774612325e0 + (0.29226079376196624949e-2 + (0.25664553693768450545e-4 + (0.19808568415654461964e-6 + (0.13351257759815557897e-8 + (0.77658124891046760667e-11 + 0.38173420035555555555e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.54, 0.55).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p54( t ) {
		return 0.16221449434620737567e0 + (0.30276865332726475672e-2 + (0.26885741326534564336e-4 + (0.20908350604346384143e-6 + (0.14151148144240728728e-8 + (0.82369170665974313027e-11 + 0.40360957457777777779e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.55, 0.56).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p55( t ) {
		return 0.16837910595412130659e0 + (0.31377844510793082301e-2 + (0.28174873844911175026e-4 + (0.22074043807045782387e-6 + (0.14999481055996090039e-8 + (0.87348993661930809254e-11 + 0.42653528977777777779e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.56, 0.57).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p56( t ) {
		return 0.17476916455659369953e0 + (0.32531815370903068316e-2 + (0.29536024347344364074e-4 + (0.23309632627767074202e-6 + (0.15899007843582444846e-8 + (0.92610375235427359475e-11 + 0.45054073102222222221e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.57, 0.58).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p57( t ) {
		return 0.18139556223643701364e0 + (0.33741744168096996041e-2 + (0.30973511714709500836e-4 + (0.24619326937592290996e-6 + (0.16852609412267750744e-8 + (0.98166442942854895573e-11 + 0.47565418097777777779e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.58, 0.59).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p58( t ) {
		return 0.18826980194443664549e0 + (0.35010775057740317997e-2 + (0.32491914440014267480e-4 + (0.26007572375886319028e-6 + (0.17863299617388376116e-8 + (0.10403065638343878679e-10 + 0.50190265831111111110e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.59, 0.60).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p59( t ) {
		return 0.19540403413693967350e0 + (0.36342240767211326315e-2 + (0.34096085096200907289e-4 + (0.27479061117017637474e-6 + (0.18934228504790032826e-8 + (0.11021679075323598664e-10 + 0.52931171733333333334e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.60, 0.61).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p60( t ) {
		return 0.20281109560651886959e0 + (0.37739673859323597060e-2 + (0.35791165457592409054e-4 + (0.29038742889416172404e-6 + (0.20068685374849001770e-8 + (0.11673891799578381999e-10 + 0.55790523093333333334e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.61, 0.62).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p61( t ) {
		return 0.21050455062669334978e0 + (0.39206818613925652425e-2 + (0.37582602289680101704e-4 + (0.30691836231886877385e-6 + (0.21270101645763677824e-8 + (0.12361138551062899455e-10 + 0.58770520160000000000e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.62, 0.63).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p62( t ) {
		return 0.21849873453703332479e0 + (0.40747643554689586041e-2 + (0.39476163820986711501e-4 + (0.32443839970139918836e-6 + (0.22542053491518680200e-8 + (0.13084879235290858490e-10 + 0.61873153262222222221e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.63, 0.64).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p63( t ) {
		return 0.22680879990043229327e0 + (0.42366354648628516935e-2 + (0.41477956909656896779e-4 + (0.34300544894502810002e-6 + (0.23888264229264067658e-8 + (0.13846596292818514601e-10 + 0.65100183751111111110e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.64, 0.65).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p64( t ) {
		return 0.23545076536988703937e0 + (0.44067409206365170888e-2 + (0.43594444916224700881e-4 + (0.36268045617760415178e-6 + (0.25312606430853202748e-8 + (0.14647791812837903061e-10 + 0.68453122631111111110e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.65, 0.66).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p65( t ) {
		return 0.24444156740777432838e0 + (0.45855530511605787178e-2 + (0.45832466292683085475e-4 + (0.38352752590033030472e-6 + (0.26819103733055603460e-8 + (0.15489984390884756993e-10 + 0.71933206364444444445e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.66, 0.67).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p66( t ) {
		return 0.25379911500634264643e0 + (0.47735723208650032167e-2 + (0.48199253896534185372e-4 + (0.40561404245564732314e-6 + (0.28411932320871165585e-8 + (0.16374705736458320149e-10 + 0.75541379822222222221e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.67, 0.68).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p67( t ) {
		return 0.26354234756393613032e0 + (0.49713289477083781266e-2 + (0.50702455036930367504e-4 + (0.42901079254268185722e-6 + (0.30095422058900481753e-8 + (0.17303497025347342498e-10 + 0.79278273368888888890e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.68, 0.69).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p68( t ) {
		return 0.27369129607732343398e0 + (0.51793846023052643767e-2 + (0.53350152258326602629e-4 + (0.45379208848865015485e-6 + (0.31874057245814381257e-8 + (0.18277905010245111046e-10 + 0.83144182364444444445e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.69, 0.70).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p69( t ) {
		return 0.28426714781640316172e0 + (0.53983341916695141966e-2 + (0.56150884865255810638e-4 + (0.48003589196494734238e-6 + (0.33752476967570796349e-8 + (0.19299477888083469086e-10 + 0.87139049137777777779e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.70, 0.71).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p70( t ) {
		return 0.29529231465348519920e0 + (0.56288077305420795663e-2 + (0.59113671189913307427e-4 + (0.50782393781744840482e-6 + (0.35735475025851713168e-8 + (0.20369760937017070382e-10 + 0.91262442613333333334e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.71, 0.72).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p71( t ) {
		return 0.30679050522528838613e0 + (0.58714723032745403331e-2 + (0.62248031602197686791e-4 + (0.53724185766200945789e-6 + (0.37827999418960232678e-8 + (0.21490291930444538307e-10 + 0.95513539182222222221e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.72, 0.73).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p72( t ) {
		return 0.31878680111173319425e0 + (0.61270341192339103514e-2 + (0.65564012259707640976e-4 + (0.56837930287837738996e-6 + (0.40035151353392378882e-8 + (0.22662596341239294792e-10 + 0.99891109760000000000e-13 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.73, 0.74).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p73( t ) {
		return 0.33130773722152622027e0 + (0.63962406646798080903e-2 + (0.69072209592942396666e-4 + (0.60133006661885941812e-6 + (0.42362183765883466691e-8 + (0.23888182347073698382e-10 + 0.10439349811555555556e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.74, 0.75).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p74( t ) {
		return 0.34438138658041336523e0 + (0.66798829540414007258e-2 + (0.72783795518603561144e-4 + (0.63619220443228800680e-6 + (0.44814499336514453364e-8 + (0.25168535651285475274e-10 + 0.10901861383111111111e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.75, 0.76).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p75( t ) {
		return 0.35803744972380175583e0 + (0.69787978834882685031e-2 + (0.76710543371454822497e-4 + (0.67306815308917386747e-6 + (0.47397647975845228205e-8 + (0.26505114141143050509e-10 + 0.11376390933333333333e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.76, 0.77).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p76( t ) {
		return 0.37230734890119724188e0 + (0.72938706896461381003e-2 + (0.80864854542670714092e-4 + (0.71206484718062688779e-6 + (0.50117323769745883805e-8 + (0.27899342394100074165e-10 + 0.11862637614222222222e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.77, 0.78).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p77( t ) {
		return 0.38722432730555448223e0 + (0.76260375162549802745e-2 + (0.85259785810004603848e-4 + (0.75329383305171327677e-6 + (0.52979361368388119355e-8 + (0.29352606054164086709e-10 + 0.12360253370666666667e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.78, 0.79).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p78( t ) {
		return 0.40282355354616940667e0 + (0.79762880915029728079e-2 + (0.89909077342438246452e-4 + (0.79687137961956194579e-6 + (0.55989731807360403195e-8 + (0.30866246101464869050e-10 + 0.12868841946666666667e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.79, 0.80).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p79( t ) {
		return 0.41914223158913787649e0 + (0.83456685186950463538e-2 + (0.94827181359250161335e-4 + (0.84291858561783141014e-6 + (0.59154537751083485684e-8 + (0.32441553034347469291e-10 + 0.13387957943111111111e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.80, 0.81).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p80( t ) {
		return 0.43621971639463786896e0 + (0.87352841828289495773e-2 + (0.10002929142066799966e-3 + (0.89156148280219880024e-6 + (0.62480008150788597147e-8 + (0.34079760983458878910e-10 + 0.13917107176888888889e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.81, 0.82).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p81( t ) {
		return 0.45409763548534330981e0 + (0.91463027755548240654e-2 + (0.10553137232446167258e-3 + (0.94293113464638623798e-6 + (0.65972492312219959885e-8 + (0.35782041795476563662e-10 + 0.14455745872000000000e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.82, 0.83).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p82( t ) {
		return 0.47282001668512331468e0 + (0.95799574408860463394e-2 + (0.11135019058000067469e-3 + (0.99716373005509038080e-6 + (0.69638453369956970347e-8 + (0.37549499088161345850e-10 + 0.15003280712888888889e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.83, 0.84).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p83( t ) {
		return 0.49243342227179841649e0 + (0.10037550043909497071e-1 + (0.11750334542845234952e-3 + (0.10544006716188967172e-5 + (0.73484461168242224872e-8 + (0.39383162326435752965e-10 + 0.15559069118222222222e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.84, 0.85).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p84( t ) {
		return 0.51298708979209258326e0 + (0.10520454564612427224e-1 + (0.12400930037494996655e-3 + (0.11147886579371265246e-5 + (0.77517184550568711454e-8 + (0.41283980931872622611e-10 + 0.16122419680000000000e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.85, 0.86).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p85( t ) {
		return 0.53453307979101369843e0 + (0.11030120618800726938e-1 + (0.13088741519572269581e-3 + (0.11784797595374515432e-5 + (0.81743383063044825400e-8 + (0.43252818449517081051e-10 + 0.16692592640000000000e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.86, 0.87).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p86( t ) {
		return 0.55712643071169299478e0 + (0.11568077107929735233e-1 + (0.13815797838036651289e-3 + (0.12456314879260904558e-5 + (0.86169898078969313597e-8 + (0.45290446811539652525e-10 + 0.17268801084444444444e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.87, 0.88).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p87( t ) {
		return 0.58082532122519320968e0 + (0.12135935999503877077e-1 + (0.14584223996665838559e-3 + (0.13164068573095710742e-5 + (0.90803643355106020163e-8 + (0.47397540713124619155e-10 + 0.17850211608888888889e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.88, 0.89).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p88( t ) {
		return 0.60569124025293375554e0 + (0.12735396239525550361e-1 + (0.15396244472258863344e-3 + (0.13909744385382818253e-5 + (0.95651595032306228245e-8 + (0.49574672127669041550e-10 + 0.18435945564444444444e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.89, 0.90).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p89( t ) {
		return 0.63178916494715716894e0 + (0.13368247798287030927e-1 + (0.16254186562762076141e-3 + (0.14695084048334056083e-5 + (0.10072078109604152350e-7 + (0.51822304995680707483e-10 + 0.19025081422222222222e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.90, 0.91).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p90( t ) {
		return 0.65918774689725319200e0 + (0.14036375850601992063e-1 + (0.17160483760259706354e-3 + (0.15521885688723188371e-5 + (0.10601827031535280590e-7 + (0.54140790105837520499e-10 + 0.19616655146666666667e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.91, 0.92).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p91( t ) {
		return 0.68795950683174433822e0 + (0.14741765091365869084e-1 + (0.18117679143520433835e-3 + (0.16392004108230585213e-5 + (0.11155116068018043001e-7 + (0.56530360194925690374e-10 + 0.20209663662222222222e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.92, 0.93).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p92( t ) {
		return 0.71818103808729967036e0 + (0.15486504187117112279e-1 + (0.19128428784550923217e-3 + (0.17307350969359975848e-5 + (0.11732656736113607751e-7 + (0.58991125287563833603e-10 + 0.20803065333333333333e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.93, 0.94).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p93( t ) {
		return 0.74993321911726254661e0 + (0.16272790364044783382e-1 + (0.20195505163377912645e-3 + (0.18269894883203346953e-5 + (0.12335161021630225535e-7 + (0.61523068312169087227e-10 + 0.21395783431111111111e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.94, 0.95).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p94( t ) {
		return 0.78330143531283492729e0 + (0.17102934132652429240e-1 + (0.21321800585063327041e-3 + (0.19281661395543913713e-5 + (0.12963340087354341574e-7 + (0.64126040998066348872e-10 + 0.21986708942222222222e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.95, 0.96).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p95( t ) {
		return 0.81837581041023811832e0 + (0.17979364149044223802e-1 + (0.22510330592753129006e-3 + (0.20344732868018175389e-5 + (0.13617902941839949718e-7 + (0.66799760083972474642e-10 + 0.22574701262222222222e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.96, 0.97).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p96( t ) {
		return 0.85525144775685126237e0 + (0.18904632212547561026e-1 + (0.23764237370371255638e-3 + (0.21461248251306387979e-5 + (0.14299555071870523786e-7 + (0.69543803864694171934e-10 + 0.23158593688888888889e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.97, 0.98).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p97( t ) {
		return 0.89402868170849933734e0 + (0.19881418399127202569e-1 + (0.25086793128395995798e-3 + (0.22633402747585233180e-5 + (0.15008997042116532283e-7 + (0.72357609075043941261e-10 + 0.23737194737777777778e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.98, 0.99).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p98( t ) {
		return 0.93481333942870796363e0 + (0.20912536329780368893e-1 + (0.26481403465998477969e-3 + (0.23863447359754921676e-5 + (0.15746923065472184451e-7 + (0.75240468141720143653e-10 + 0.24309291271111111111e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for \[0.99, 1.00).
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p99( t ) {
		return 0.97771701335885035464e0 + (0.22000938572830479551e-1 + (0.27951610702682383001e-3 + (0.25153688325245314530e-5 + (0.16514019547822821453e-7 + (0.78191526829368231251e-10 + 0.24873652355555555556e-12 * t) * t) * t) * t) * t) * t;
	}

	/**
	* Chebyshev polynomial for `1.0`.
	*
	* @private
	* @param {number} t - scaled value
	* @returns {number} result
	*/
	function p100() {
		// We only get here if y = 1; i.e., |x| < 4*eps, in which case `erfcx` is within `1e-15` of 1...
		return 1.0;
	}


	// MAIN //

	/**
	* Given `y100 = 100*y`, where `y = 4/(4+x)` for `x >= 0`, compute `erfc(x)`.
	*
	* ## Notes
	*
	* -   Uses a look-up table of 100 different Chebyshev polynomials or y intervals \[0,0.01\], \[0.01,0.02\], ..., \[0.99,1\], generated with the help of Maple and a little shell script.
	* -   This allows the Chebyshev polynomials to be of significantly lower degree (about 1/4) compared to fitting the whole \[0,1\] interval with a single polynomial.
	*
	* @private
	* @param {number} y100 - scaled argument
	* @returns {number} complementary error function
	*/
	function erfcxY100( y100 ) {
		var t = floor( y100 );
		var f = table[ t ];
		return f( 2*y100 - ((2*t) + 1) );
	}


	// EXPORTS //

	erfcx_y100 = erfcxY100;
	return erfcx_y100;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
* ## Notice
*
* The following copyright, license, and long comment were part of the original implementation available as part of [SciPy]{@link https://github.com/scipy/scipy/blob/ed14bf0a66440a4d164581499fda662121963a56/scipy/special/Faddeeva.cc}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (c) 2012 Massachusetts Institute of Technology
*
* Permission is hereby granted, free of charge, to any person obtaining
* a copy of this software and associated documentation files (the
* "Software"), to deal in the Software without restriction, including
* without limitation the rights to use, copy, modify, merge, publish,
* distribute, sublicense, and/or sell copies of the Software, and to
* permit persons to whom the Software is furnished to do so, subject to
* the following conditions:
*
* The above copyright notice and this permission notice shall be
* included in all copies or substantial portions of the Software.
*
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
* EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
* MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
* NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
* LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
* OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
* WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
* ```
*/

var main$i;
var hasRequiredMain$i;

function requireMain$i () {
	if (hasRequiredMain$i) return main$i;
	hasRequiredMain$i = 1;

	// MODULES //

	var exp = requireLib$L();
	var PINF = requireLib$1m();
	var erfcxY100 = requireErfcx_y100();


	// VARIABLES //

	var INV_SQRT_PI = 0.56418958354775628694807945156; // 1 / sqrt(pi)


	// MAIN //

	/**
	* Evaluates the scaled complementary error function.
	*
	* ```tex
	* \operatorname{erfcx}(x) = \exp{x^2} \cdot \operatorname{erfc}(x)
	* ```
	*
	* ## Notes
	*
	* -   Use this function to replace expressions containing `exp{x^2} erfc(x)` in order to avoid errors due to underflow or overflow.
	* -   For expressions of the form `\exp{-x^2} erfcx(x)`, use the complementary error function `erfc(x)` instead, as this substitution maintains accuracy by avoiding roundoff errors for large values of `x`.
	*
	* @param {number} x - input value
	* @returns {number} evaluated scaled complementary error function
	*
	* @example
	* var y = erfcx( 0.0 );
	* // returns 1.0
	*
	* @example
	* var y = erfcx( 1.0 );
	* // returns ~0.4276
	*
	* @example
	* var y = erfcx( -1.0 );
	* // returns ~5.01
	*
	* @example
	* var y = erfcx( 50.0 );
	* // returns ~0.011
	*
	* @example
	* var y = erfcx( -50.0 );
	* // returns +Infinity
	*
	* @example
	* var y = erfcx( NaN );
	* // returns NaN
	*/
	function erfcx( x ) {
		var x2;
		if ( x !== x ) {
			return NaN;
		}
		if ( x >= 0.0 ) {
			if ( x > 50.0 ) { // continued-fraction expansion is faster
				if ( x > 5.0e7 ) { // 1-term expansion, important to avoid overflow
					return INV_SQRT_PI / x;
				}
				x2 = x * x;

				// 5-term expansion (rely on compiler for CSE), simplified from: INV_SQRT_PI / (x+0.5/(x+1/(x+1.5/(x+2/x))))
				return INV_SQRT_PI * ( ( x2 * (x2+4.5) ) + 2.0 ) / ( x * ( ( x2*(x2+5.0) ) + 3.75 ) ); // eslint-disable-line max-len
			}
			return erfcxY100( 400.0/(4.0+x) );
		}
		if ( x < -26.7 ) {
			return PINF;
		}
		x2 = x * x;
		if ( x < -6.1 ) {
			return 2.0 * exp( x2 );
		}
		return ( 2.0*exp( x2 ) ) - erfcxY100( 400.0/(4.0-x) );
	}


	// EXPORTS //

	main$i = erfcx;
	return main$i;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$m;
var hasRequiredLib$m;

function requireLib$m () {
	if (hasRequiredLib$m) return lib$m;
	hasRequiredLib$m = 1;

	/**
	* Evaluate the scaled complementary error function.
	*
	* @module @stdlib/math-base-special-erfcx
	*
	* @example
	* var erfcx = require( '@stdlib/math-base-special-erfcx' );
	*
	* var y = erfcx( 0.0 );
	* // returns 1.0
	*
	* y = erfcx( 50.0 );
	* // returns ~0.011
	*
	* y = erfcx( -50.0 );
	* // returns +Infinity
	*/

	// MODULES //

	var main = requireMain$i();


	// EXPORTS //

	lib$m = main;
	return lib$m;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$h;
var hasRequiredMain$h;

function requireMain$h () {
	if (hasRequiredMain$h) return main$h;
	hasRequiredMain$h = 1;

	// MODULES //

	var ln = requireLib$z();
	var log1p = requireLib$o();
	var abs2 = requireLib$n();
	var erfc = requireLib$J();
	var erfcx = requireLib$m();
	var NINF = requireLib$1n();
	var isnan = requireLib$1s();


	// VARIABLES //

	var INV_SQRT_TWO = 0.7071067811865475; // 1/sqrt(2)


	// MAIN //

	/**
	* Evaluates the natural logarithm of the cumulative distribution function (CDF) for a normal distribution with mean `mu` and standard deviation `sigma` at a value `x`.
	*
	* @param {number} x - input value
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {number} logarithm of cumulative distribution function
	*
	* @example
	* var y = logcdf( 2.0, 0.0, 1.0 );
	* // returns ~-0.023
	*
	* @example
	* var y = logcdf( -1.0, 4.0, 2.0 );
	* // returns ~-5.082
	*
	* @example
	* var y = logcdf( NaN, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = logcdf( 0.0, NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = logcdf( 0.0, 0.0, NaN );
	* // returns NaN
	*
	* @example
	* // Negative standard deviation:
	* var y = logcdf( 2.0, 0.0, -1.0 );
	* // returns NaN
	*
	* @example
	* var y = logcdf( 2.0, 8.0, 0.0 );
	* // returns -Infinity
	*
	* @example
	* var y = logcdf( 8.0, 8.0, 0.0 );
	* // returns 0.0
	*/
	function logcdf( x, mu, sigma ) {
		var z;
		if (
			isnan( x ) ||
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return NaN;
		}
		if ( sigma === 0.0 ) {
			return (x < mu) ? NINF : 0.0;
		}
		z = ( x - mu ) / sigma;
		if ( z < -1.0 ) {
			return ln( erfcx( -z * INV_SQRT_TWO ) / 2.0 ) - ( abs2(z) / 2.0 );
		}
		// Case: z >= -1.0:
		return log1p( -erfc( z * INV_SQRT_TWO ) / 2.0 );
	}


	// EXPORTS //

	main$h = logcdf;
	return main$h;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$g;
var hasRequiredMain$g;

function requireMain$g () {
	if (hasRequiredMain$g) return main$g;
	hasRequiredMain$g = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var NINF = requireLib$1n();


	// MAIN //

	/**
	* Evaluates the natural logarithm of the cumulative distribution function (logCDF) for a degenerate distribution with mean `mu`.
	*
	* @param {number} x - input value
	* @param {number} mu - constant value of distribution
	* @returns {number} natural logarithm of cumulative distribution function
	*
	* @example
	* var y = logcdf( 2.0, 3.0 );
	* // returns -Infinity
	*
	* @example
	* var y = logcdf( 4.0, 3.0 );
	* // returns 0.0
	*
	* @example
	* var y = logcdf( 3.0, 3.0 );
	* // returns 0.0
	*
	* @example
	* var y = logcdf( NaN, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = logcdf( 0.0, NaN );
	* // returns NaN
	*/
	function logcdf( x, mu ) {
		if ( isnan( x ) || isnan( mu ) ) {
			return NaN;
		}
		return ( x < mu ) ? NINF : 0.0;
	}


	// EXPORTS //

	main$g = logcdf;
	return main$g;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$8;
var hasRequiredFactory$8;

function requireFactory$8 () {
	if (hasRequiredFactory$8) return factory_1$8;
	hasRequiredFactory$8 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var isnan = requireLib$1s();
	var NINF = requireLib$1n();


	// MAIN //

	/**
	* Returns a function for evaluating the natural logarithm of the cumulative distribution function (logCDF) of a degenerate distribution centered at a provided mean value.
	*
	* @param {number} mu - value at which to center the distribution
	* @returns {Function} logCDF
	*
	* @example
	* var logcdf = factory( 5.0 );
	*
	* var y = logcdf( 3.0 );
	* // returns -Infinity
	*
	* y = logcdf( 6.0 );
	* // returns 0.0
	*
	* y = logcdf( NaN );
	* // returns NaN
	*/
	function factory( mu ) {
		if ( isnan( mu ) ) {
			return constantFunction( NaN );
		}
		return logcdf;

		/**
		* Evaluates the natural logarithm of the cumulative distribution function (logCDF) of a degenerate distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {number} natural logarithm of cumulative distribution function
		*
		* @example
		* var y = logcdf( 10.0 );
		* // returns <number>
		*/
		function logcdf( x ) {
			if ( isnan( x ) ) {
				return NaN;
			}
			return ( x < mu ) ? NINF : 0.0;
		}
	}


	// EXPORTS //

	factory_1$8 = factory;
	return factory_1$8;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$l;
var hasRequiredLib$l;

function requireLib$l () {
	if (hasRequiredLib$l) return lib$l;
	hasRequiredLib$l = 1;

	/**
	* Degenerate distribution logarithm of cumulative distribution function (CDF).
	*
	* @module @stdlib/stats-base-dists-degenerate-logcdf
	*
	* @example
	* var logcdf = require( '@stdlib/stats-base-dists-degenerate-logcdf' );
	*
	* var y = logcdf( 2.0, 5.0 );
	* // returns -Infinity
	*
	* @example
	* var factory = require( '@stdlib/stats-base-dists-degenerate-logcdf' ).factory;
	*
	* var logcdf = factory( 5.0 );
	*
	* var y = logcdf( 3.0 );
	* // returns -Infinity
	*
	* y = logcdf( 6.0 );
	* // returns 0.0
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$g();
	var factory = requireFactory$8();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$l = main;
	return lib$l;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$7;
var hasRequiredFactory$7;

function requireFactory$7 () {
	if (hasRequiredFactory$7) return factory_1$7;
	hasRequiredFactory$7 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var degenerate = requireLib$l().factory;
	var isnan = requireLib$1s();
	var log1p = requireLib$o();
	var abs2 = requireLib$n();
	var erfc = requireLib$J();
	var erfcx = requireLib$m();
	var ln = requireLib$z();


	// VARIABLES //

	var INV_SQRT_TWO = 0.7071067811865475; // 1/sqrt(2)


	// MAIN //

	/**
	* Returns a function for evaluating the natural logarithm of the cumulative distribution function (CDF) for a normal distribution.
	*
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {Function} logcdf
	*
	* @example
	* var logcdf = factory( 10.0, 2.0 );
	* var y = logcdf( 10.0 );
	* // returns ~-0.693
	*
	* y = logcdf( 5.0 );
	* // returns ~-5.082
	*/
	function factory( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return constantFunction( NaN );
		}
		if ( sigma === 0.0 ) {
			return degenerate( mu );
		}

		return logcdf;

		/**
		* Evaluates the natural logarithm of the cumulative distribution function (CDF) for a normal distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {number} evaluated logcdf
		*
		* @example
		* var y = logcdf( -3.14 );
		* // returns <number>
		*/
		function logcdf( x ) {
			var z = ( x - mu ) / sigma;
			if ( z < -1.0 ) {
				return ln( erfcx( -z * INV_SQRT_TWO ) / 2.0 ) - ( abs2(z) / 2.0 );
			}
			// Case: z >= -1.0:
			return log1p( -erfc( z * INV_SQRT_TWO ) / 2.0 );
		}
	}


	// EXPORTS //

	factory_1$7 = factory;
	return factory_1$7;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$k;
var hasRequiredLib$k;

function requireLib$k () {
	if (hasRequiredLib$k) return lib$k;
	hasRequiredLib$k = 1;

	/**
	* Natural logarithm of the cumulative distribution function (CDF) for a normal distribution.
	*
	* @module @stdlib/stats-base-dists-normal-logcdf
	*
	* @example
	* var logcdf = require( '@stdlib/stats-base-dists-normal-logcdf' );
	*
	* var y = logcdf( 2.0, 0.0, 1.0 );
	* // returns ~-0.023
	*
	* var mylogcdf = logcdf.factory( 10.0, 2.0 );
	* y = mylogcdf( 10.0 );
	* // returns ~-0.693
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$h();
	var factory = requireFactory$7();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$k = main;
	return lib$k;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$f;
var hasRequiredMain$f;

function requireMain$f () {
	if (hasRequiredMain$f) return main$f;
	hasRequiredMain$f = 1;

	// MODULES //

	var floor = requireLib$1r();


	// MAIN //

	/**
	* Tests if a finite double-precision floating-point number is an integer.
	*
	* @param {number} x - value to test
	* @returns {boolean} boolean indicating whether the value is an integer
	*
	* @example
	* var bool = isInteger( 1.0 );
	* // returns true
	*
	* @example
	* var bool = isInteger( 3.14 );
	* // returns false
	*/
	function isInteger( x ) {
		return (floor(x) === x);
	}


	// EXPORTS //

	main$f = isInteger;
	return main$f;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$j;
var hasRequiredLib$j;

function requireLib$j () {
	if (hasRequiredLib$j) return lib$j;
	hasRequiredLib$j = 1;

	/**
	* Test if a finite double-precision floating-point number is an integer.
	*
	* @module @stdlib/math-base-assert-is-integer
	*
	* @example
	* var isInteger = require( '@stdlib/math-base-assert-is-integer' );
	*
	* var bool = isInteger( 1.0 );
	* // returns true
	*
	* bool = isInteger( 3.14 );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$f();


	// EXPORTS //

	lib$j = main;
	return lib$j;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$e;
var hasRequiredMain$e;

function requireMain$e () {
	if (hasRequiredMain$e) return main$e;
	hasRequiredMain$e = 1;

	// MODULES //

	var isInteger = requireLib$j();


	// MAIN //

	/**
	* Tests if a finite numeric value is an even number.
	*
	* @param {number} x - value to test
	* @returns {boolean} boolean indicating whether the value is an even number
	*
	* @example
	* var bool = isEven( 5.0 );
	* // returns false
	*
	* @example
	* var bool = isEven( -2.0 );
	* // returns true
	*
	* @example
	* var bool = isEven( 0.0 );
	* // returns true
	*
	* @example
	* var bool = isEven( NaN );
	* // returns false
	*/
	function isEven( x ) {
		return isInteger( x/2.0 );
	}


	// EXPORTS //

	main$e = isEven;
	return main$e;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$i;
var hasRequiredLib$i;

function requireLib$i () {
	if (hasRequiredLib$i) return lib$i;
	hasRequiredLib$i = 1;

	/**
	* Test if a finite numeric value is an even number.
	*
	* @module @stdlib/math-base-assert-is-even
	*
	* @example
	* var isEven = require( '@stdlib/math-base-assert-is-even' );
	*
	* var bool = isEven( 5.0 );
	* // returns false
	*
	* bool = isEven( -2.0 );
	* // returns true
	*
	* bool = isEven( 0.0 );
	* // returns true
	*
	* bool = isEven( NaN );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$e();


	// EXPORTS //

	lib$i = main;
	return lib$i;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$d;
var hasRequiredMain$d;

function requireMain$d () {
	if (hasRequiredMain$d) return main$d;
	hasRequiredMain$d = 1;

	// MODULES //

	var isEven = requireLib$i();


	// MAIN //

	/**
	* Tests if a finite numeric value is an odd number.
	*
	* @param {number} x - value to test
	* @returns {boolean} boolean indicating whether the value is an odd number
	*
	* @example
	* var bool = isOdd( 5.0 );
	* // returns true
	*
	* @example
	* var bool = isOdd( -2.0 );
	* // returns false
	*
	* @example
	* var bool = isOdd( 0.0 );
	* // returns false
	*
	* @example
	* var bool = isOdd( NaN );
	* // returns false
	*/
	function isOdd( x ) {
		// Check sign to prevent overflow...
		if ( x > 0.0 ) {
			return isEven( x-1.0 );
		}
		return isEven( x+1.0 );
	}


	// EXPORTS //

	main$d = isOdd;
	return main$d;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$h;
var hasRequiredLib$h;

function requireLib$h () {
	if (hasRequiredLib$h) return lib$h;
	hasRequiredLib$h = 1;

	/**
	* Test if a finite numeric value is an odd number.
	*
	* @module @stdlib/math-base-assert-is-odd
	*
	* @example
	* var isOdd = require( '@stdlib/math-base-assert-is-odd' );
	*
	* var bool = isOdd( 5.0 );
	* // returns true
	*
	* bool = isOdd( -2.0 );
	* // returns false
	*
	* bool = isOdd( 0.0 );
	* // returns false
	*
	* bool = isOdd( NaN );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$d();


	// EXPORTS //

	lib$h = main;
	return lib$h;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$c;
var hasRequiredMain$c;

function requireMain$c () {
	if (hasRequiredMain$c) return main$c;
	hasRequiredMain$c = 1;

	// MAIN //

	/**
	* Converts an unsigned 32-bit integer to a signed 32-bit integer.
	*
	* @param {uinteger32} x - unsigned 32-bit integer
	* @returns {integer32} signed 32-bit integer
	*
	* @example
	* var float64ToUint32 = require( '@stdlib/number-float64-base-to-uint32' );
	* var y = uint32ToInt32( float64ToUint32( 4294967295 ) );
	* // returns -1
	*
	* @example
	* var float64ToUint32 = require( '@stdlib/number-float64-base-to-uint32' );
	* var y = uint32ToInt32( float64ToUint32( 3 ) );
	* // returns 3
	*/
	function uint32ToInt32( x ) {
		// NOTE: we could also use typed-arrays to achieve the same end.
		return x|0; // asm type annotation
	}


	// EXPORTS //

	main$c = uint32ToInt32;
	return main$c;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$g;
var hasRequiredLib$g;

function requireLib$g () {
	if (hasRequiredLib$g) return lib$g;
	hasRequiredLib$g = 1;

	/**
	* Convert an unsigned 32-bit integer to a signed 32-bit integer.
	*
	* @module @stdlib/number-uint32-base-to-int32
	*
	* @example
	* var float64ToUint32 = require( '@stdlib/number-float64-base-to-uint32' );
	* var uint32ToInt32 = require( '@stdlib/number-uint32-base-to-int32' );
	*
	* var y = uint32ToInt32( float64ToUint32( 4294967295 ) );
	* // returns -1
	*
	* y = uint32ToInt32( float64ToUint32( 3 ) );
	* // returns 3
	*/

	// MODULES //

	var uint32ToInt32 = requireMain$c();


	// EXPORTS //

	lib$g = uint32ToInt32;
	return lib$g;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var x_is_zero$1;
var hasRequiredX_is_zero$1;

function requireX_is_zero$1 () {
	if (hasRequiredX_is_zero$1) return x_is_zero$1;
	hasRequiredX_is_zero$1 = 1;

	// MODULES //

	var isOdd = requireLib$h();
	var copysign = requireLib$S();
	var NINF = requireLib$1n();
	var PINF = requireLib$1m();


	// MAIN //

	/**
	* Evaluates the exponential function when \\(|x| = 0\\).
	*
	* @private
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} function value
	*
	* @example
	* var v = pow( 0.0, 2 );
	* // returns 0.0
	*
	* @example
	* var v = pow( -0.0, -9 );
	* // returns -Infinity
	*
	* @example
	* var v = pow( 0.0, -9 );
	* // returns Infinity
	*
	* @example
	* var v = pow( -0.0, 9 );
	* // returns 0.0
	*
	* @example
	* var v = pow( 0.0, -Infinity  );
	* // returns Infinity
	*
	* @example
	* var v = pow( 0.0, Infinity );
	* // returns 0.0
	*/
	function pow( x, y ) {
		if ( y === NINF ) {
			return PINF;
		}
		if ( y === PINF ) {
			return 0.0;
		}
		if ( y > 0.0 ) {
			if ( isOdd( y ) ) {
				return x; // handles +-0
			}
			return 0.0;
		}
		// y < 0.0
		if ( isOdd( y ) ) {
			return copysign( PINF, x ); // handles +-0
		}
		return PINF;
	}


	// EXPORTS //

	x_is_zero$1 = pow;
	return x_is_zero$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var y_is_huge$1;
var hasRequiredY_is_huge$1;

function requireY_is_huge$1 () {
	if (hasRequiredY_is_huge$1) return y_is_huge$1;
	hasRequiredY_is_huge$1 = 1;

	// MODULES //

	var ABS_MASK = requireLib$1f();
	var getHighWord = requireLib$U();


	// VARIABLES //

	// 0x3fefffff = 1072693247 => 0 01111111110 11111111111111111111 => biased exponent: 1022 = -1+1023 => 2^-1
	var HIGH_MAX_NEAR_UNITY = 0x3fefffff|0; // asm type annotation

	var HUGE = 1.0e300;
	var TINY = 1.0e-300;


	// MAIN //

	/**
	* Evaluates the exponential function when \\(|y| > 2^64\\).
	*
	* @private
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} overflow or underflow result
	*
	* @example
	* var v = pow( 9.0, 3.6893488147419103e19 );
	* // returns Infinity
	*
	* @example
	* var v = pow( -3.14, -3.6893488147419103e19 );
	* // returns 0.0
	*/
	function pow( x, y ) {
		var ahx;
		var hx;

		hx = getHighWord( x );
		ahx = (hx & ABS_MASK);

		if ( ahx <= HIGH_MAX_NEAR_UNITY ) {
			if ( y < 0 ) {
				// Signal overflow...
				return HUGE * HUGE;
			}
			// Signal underflow...
			return TINY * TINY;
		}
		// `x` has a biased exponent greater than or equal to `0`...

		if ( y > 0 ) {
			// Signal overflow...
			return HUGE * HUGE;
		}
		// Signal underflow...
		return TINY * TINY;
	}


	// EXPORTS //

	y_is_huge$1 = pow;
	return y_is_huge$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var y_is_infinite$1;
var hasRequiredY_is_infinite$1;

function requireY_is_infinite$1 () {
	if (hasRequiredY_is_infinite$1) return y_is_infinite$1;
	hasRequiredY_is_infinite$1 = 1;

	// MODULES //

	var abs = requireLib$Q();
	var PINF = requireLib$1m();


	// MAIN //

	/**
	* Evaluates the exponential function when \\( y = \pm \infty\\).
	*
	* @private
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} function value
	*
	* @example
	* var v = pow( -1.0, Infinity );
	* // returns NaN
	*
	* @example
	* var v = pow( -1.0, -Infinity  );
	* // returns NaN
	*
	* @example
	* var v = pow( 1.0, Infinity );
	* // returns 1.0
	*
	* @example
	* var v = pow( 1.0, -Infinity  );
	* // returns 1.0
	*
	* @example
	* var v = pow( 0.5, Infinity );
	* // returns 0.0
	*
	* @example
	* var v = pow( 0.5, -Infinity  );
	* // returns Infinity
	*
	* @example
	* var v = pow( 1.5, -Infinity  );
	* // returns 0.0
	*
	* @example
	* var v = pow( 1.5, Infinity );
	* // returns Infinity
	*/
	function pow( x, y ) {
		if ( x === -1.0 ) {
			// Julia (0.4.2) and Python (2.7.9) return `1.0` (WTF???). JavaScript (`Math.pow`), R, and libm return `NaN`. We choose `NaN`, as the value is indeterminate; i.e., we cannot determine whether `y` is odd, even, or somewhere in between.
			return (x-x)/(x-x); // signal NaN
		}
		if ( x === 1.0 ) {
			return 1.0;
		}
		// (|x| > 1 && y === NINF) || (|x| < 1 && y === PINF)
		if ( (abs(x) < 1.0) === (y === PINF) ) {
			return 0.0;
		}
		// (|x| > 1 && y === PINF) || (|x| < 1 && y === NINF)
		return PINF;
	}


	// EXPORTS //

	y_is_infinite$1 = pow;
	return y_is_infinite$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_l$1;
var hasRequiredPolyval_l$1;

function requirePolyval_l$1 () {
	if (hasRequiredPolyval_l$1) return polyval_l$1;
	hasRequiredPolyval_l$1 = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.5999999999999946;
		}
		return 0.5999999999999946 + (x * (0.4285714285785502 + (x * (0.33333332981837743 + (x * (0.272728123808534 + (x * (0.23066074577556175 + (x * 0.20697501780033842))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_l$1 = evalpoly;
	return polyval_l$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var log2ax_1$1;
var hasRequiredLog2ax$1;

function requireLog2ax$1 () {
	if (hasRequiredLog2ax$1) return log2ax_1$1;
	hasRequiredLog2ax$1 = 1;

	// MODULES //

	var getHighWord = requireLib$U();
	var setLowWord = requireLib$K();
	var setHighWord = requireLib$A();
	var BIAS = requireLib$1l();
	var polyvalL = requirePolyval_l$1();


	// VARIABLES //

	// 0x000fffff = 1048575 => 0 00000000000 11111111111111111111
	var HIGH_SIGNIFICAND_MASK = 0x000fffff|0; // asm type annotation

	// 0x00100000 = 1048576 => 0 00000000001 00000000000000000000 => biased exponent: 1 = -1022+1023 => 2^-1022
	var HIGH_MIN_NORMAL_EXP = 0x00100000|0; // asm type annotation

	// 0x3ff00000 = 1072693248 => 0 01111111111 00000000000000000000 => biased exponent: 1023 = 0+1023 => 2^0 = 1
	var HIGH_BIASED_EXP_0 = 0x3ff00000|0; // asm type annotation

	// 0x20000000 = 536870912 => 0 01000000000 00000000000000000000 => biased exponent: 512 = -511+1023
	var HIGH_BIASED_EXP_NEG_512 = 0x20000000|0; // asm type annotation

	// 0x00080000 = 524288 => 0 00000000000 10000000000000000000
	var HIGH_SIGNIFICAND_HALF = 0x00080000|0; // asm type annotation

	// TODO: consider making an external constant
	var HIGH_NUM_SIGNIFICAND_BITS = 20|0; // asm type annotation

	var TWO53 = 9007199254740992.0;	// 0x43400000, 0x00000000

	// 2/(3*LN2)
	var CP = 9.61796693925975554329e-01; // 0x3FEEC709, 0xDC3A03FD

	// (float)CP
	var CP_HI = 9.61796700954437255859e-01; // 0x3FEEC709, 0xE0000000

	// Low: CP_HI
	var CP_LO = -7.02846165095275826516e-09; // 0xBE3E2FE0, 0x145B01F5

	var BP = [
		1.0,
		1.5
	];
	var DP_HI = [
		0.0,
		5.84962487220764160156e-01 // 0x3FE2B803, 0x40000000
	];
	var DP_LO = [
		0.0,
		1.35003920212974897128e-08 // 0x3E4CFDEB, 0x43CFD006
	];


	// MAIN //

	/**
	* Computes \\(\operatorname{log2}(ax)\\).
	*
	* @private
	* @param {Array} out - output array
	* @param {number} ax - absolute value of `x`
	* @param {number} ahx - high word of `ax`
	* @returns {Array} output array containing a tuple comprised of high and low parts
	*
	* @example
	* var t = log2ax( [ 0.0, 0.0 ], 9.0, 1075970048 ); // => [ t1, t2 ]
	* // returns [ 3.169923782348633, 0.0000012190936795504075 ]
	*/
	function log2ax( out, ax, ahx ) {
		var tmp;
		var ss; // `hs + ls`
		var s2; // `ss` squared
		var hs;
		var ls;
		var ht;
		var lt;
		var bp; // `BP` constant
		var dp; // `DP` constant
		var hp;
		var lp;
		var hz;
		var lz;
		var t1;
		var t2;
		var t;
		var r;
		var u;
		var v;
		var n;
		var j;
		var k;

		n = 0|0; // asm type annotation

		// Check if `x` is subnormal...
		if ( ahx < HIGH_MIN_NORMAL_EXP ) {
			ax *= TWO53;
			n -= 53|0; // asm type annotation
			ahx = getHighWord( ax );
		}
		// Extract the unbiased exponent of `x`:
		n += ((ahx >> HIGH_NUM_SIGNIFICAND_BITS) - BIAS)|0; // asm type annotation

		// Isolate the significand bits of `x`:
		j = (ahx & HIGH_SIGNIFICAND_MASK)|0; // asm type annotation

		// Normalize `ahx` by setting the (biased) exponent to `1023`:
		ahx = (j | HIGH_BIASED_EXP_0)|0; // asm type annotation

		// Determine the interval of `|x|` by comparing significand bits...

		// |x| < sqrt(3/2)
		if ( j <= 0x3988E ) { // 0 00000000000 00111001100010001110
			k = 0;
		}
		// |x| < sqrt(3)
		else if ( j < 0xBB67A ) { // 0 00000000000 10111011011001111010
			k = 1;
		}
		// |x| >= sqrt(3)
		else {
			k = 0;
			n += 1|0; // asm type annotation
			ahx -= HIGH_MIN_NORMAL_EXP;
		}
		// Load the normalized high word into `|x|`:
		ax = setHighWord( ax, ahx );

		// Compute `ss = hs + ls = (x-1)/(x+1)` or `(x-1.5)/(x+1.5)`:
		bp = BP[ k ]; // BP[0] = 1.0, BP[1] = 1.5
		u = ax - bp; // (x-1) || (x-1.5)
		v = 1.0 / (ax + bp); // 1/(x+1) || 1/(x+1.5)
		ss = u * v;
		hs = setLowWord( ss, 0 ); // set all low word (less significant significand) bits to 0s

		// Compute `ht = ax + bp` (via manipulation, i.e., bit flipping, of the high word):
		tmp = ((ahx>>1) | HIGH_BIASED_EXP_NEG_512) + HIGH_SIGNIFICAND_HALF;
		tmp += (k << 18); // `(k<<18)` can be considered the word equivalent of `1.0` or `1.5`
		ht = setHighWord( 0.0, tmp );
		lt = ax - (ht - bp);
		ls = v * ( ( u - (hs*ht) ) - ( hs*lt ) );

		// Compute `log(ax)`...

		s2 = ss * ss;
		r = s2 * s2 * polyvalL( s2 );
		r += ls * (hs + ss);
		s2 = hs * hs;
		ht = 3.0 + s2 + r;
		ht = setLowWord( ht, 0 );
		lt = r - ((ht-3.0) - s2);

		// u+v = ss*(1+...):
		u = hs * ht;
		v = ( ls*ht ) + ( lt*ss );

		// 2/(3LN2) * (ss+...):
		hp = u + v;
		hp = setLowWord( hp, 0 );
		lp = v - (hp - u);
		hz = CP_HI * hp; // CP_HI+CP_LO = 2/(3*LN2)
		lz = ( CP_LO*hp ) + ( lp*CP ) + DP_LO[ k ];

		// log2(ax) = (ss+...)*2/(3*LN2) = n + dp + hz + lz
		dp = DP_HI[ k ];
		t = n;
		t1 = ((hz+lz) + dp) + t; // log2(ax)
		t1 = setLowWord( t1, 0 );
		t2 = lz - (((t1-t) - dp) - hz);

		out[ 0 ] = t1;
		out[ 1 ] = t2;
		return out;
	}


	// EXPORTS //

	log2ax_1$1 = log2ax;
	return log2ax_1$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_w$1;
var hasRequiredPolyval_w$1;

function requirePolyval_w$1 () {
	if (hasRequiredPolyval_w$1) return polyval_w$1;
	hasRequiredPolyval_w$1 = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.5;
		}
		return 0.5 + (x * (-0.3333333333333333 + (x * 0.25)));
	}


	// EXPORTS //

	polyval_w$1 = evalpoly;
	return polyval_w$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var logx_1$1;
var hasRequiredLogx$1;

function requireLogx$1 () {
	if (hasRequiredLogx$1) return logx_1$1;
	hasRequiredLogx$1 = 1;

	// MODULES //

	var setLowWord = requireLib$K();
	var polyvalW = requirePolyval_w$1();


	// VARIABLES //

	// 1/LN2
	var INV_LN2 = 1.44269504088896338700e+00; // 0x3FF71547, 0x652B82FE

	// High (24 bits): 1/LN2
	var INV_LN2_HI = 1.44269502162933349609e+00; // 0x3FF71547, 0x60000000

	// Low: 1/LN2
	var INV_LN2_LO = 1.92596299112661746887e-08; // 0x3E54AE0B, 0xF85DDF44


	// MAIN //

	/**
	* Computes \\(\operatorname{log}(x)\\) assuming \\(|1-x|\\) is small and using the approximation \\(x - x^2/2 + x^3/3 - x^4/4\\).
	*
	* @private
	* @param {Array} out - output array
	* @param {number} ax - absolute value of `x`
	* @returns {Array} output array containing a tuple comprised of high and low parts
	*
	* @example
	* var t = logx( [ 0.0, 0.0 ], 9.0 ); // => [ t1, t2 ]
	* // returns [ -1265.7236328125, -0.0008163940840404393 ]
	*/
	function logx( out, ax ) {
		var t2;
		var t1;
		var t;
		var w;
		var u;
		var v;

		t = ax - 1.0; // `t` has `20` trailing zeros
		w = t * t * polyvalW( t );
		u = INV_LN2_HI * t; // `INV_LN2_HI` has `21` significant bits
		v = ( t*INV_LN2_LO ) - ( w*INV_LN2 );
		t1 = u + v;
		t1 = setLowWord( t1, 0 );
		t2 = v - (t1 - u);

		out[ 0 ] = t1;
		out[ 1 ] = t2;
		return out;
	}


	// EXPORTS //

	logx_1$1 = logx;
	return logx_1$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$f;
var hasRequiredLib$f;

function requireLib$f () {
	if (hasRequiredLib$f) return lib$f;
	hasRequiredLib$f = 1;

	/**
	* Natural logarithm of `2`.
	*
	* @module @stdlib/constants-float64-ln-two
	* @type {number}
	*
	* @example
	* var LN2 = require( '@stdlib/constants-float64-ln-two' );
	* // returns 0.6931471805599453
	*/


	// MAIN //

	/**
	* Natural logarithm of `2`.
	*
	* ```tex
	* \ln 2
	* ```
	*
	* @constant
	* @type {number}
	* @default 0.6931471805599453
	*/
	var LN2 = 6.93147180559945309417232121458176568075500134360255254120680009493393621969694715605863326996418687542001481021e-01; // eslint-disable-line max-len


	// EXPORTS //

	lib$f = LN2;
	return lib$f;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$e;
var hasRequiredLib$e;

function requireLib$e () {
	if (hasRequiredLib$e) return lib$e;
	hasRequiredLib$e = 1;

	/**
	* High word mask for the significand of a double-precision floating-point number.
	*
	* @module @stdlib/constants-float64-high-word-significand-mask
	* @type {uinteger32}
	*
	* @example
	* var FLOAT64_HIGH_WORD_SIGNIFICAND_MASK = require( '@stdlib/constants-float64-high-word-significand-mask' );
	* // returns 1048575
	*/


	// MAIN //

	/**
	* High word mask for the significand of a double-precision floating-point number.
	*
	* ## Notes
	*
	* The high word mask for the significand of a double-precision floating-point number is an unsigned 32-bit integer with the value \\( 1048575 \\), which corresponds to the bit sequence
	*
	* ```binarystring
	* 0 00000000000 11111111111111111111
	* ```
	*
	* @constant
	* @type {uinteger32}
	* @default 0x000fffff
	* @see [IEEE 754]{@link https://en.wikipedia.org/wiki/IEEE_754-1985}
	*/
	var FLOAT64_HIGH_WORD_SIGNIFICAND_MASK = 0x000fffff;


	// EXPORTS //

	lib$e = FLOAT64_HIGH_WORD_SIGNIFICAND_MASK;
	return lib$e;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_p$1;
var hasRequiredPolyval_p$1;

function requirePolyval_p$1 () {
	if (hasRequiredPolyval_p$1) return polyval_p$1;
	hasRequiredPolyval_p$1 = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.16666666666666602;
		}
		return 0.16666666666666602 + (x * (-0.0027777777777015593 + (x * (0.00006613756321437934 + (x * (-0.0000016533902205465252 + (x * 4.1381367970572385e-8))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_p$1 = evalpoly;
	return polyval_p$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var pow2_1$1;
var hasRequiredPow2$1;

function requirePow2$1 () {
	if (hasRequiredPow2$1) return pow2_1$1;
	hasRequiredPow2$1 = 1;

	// MODULES //

	var getHighWord = requireLib$U();
	var setHighWord = requireLib$A();
	var setLowWord = requireLib$K();
	var uint32ToInt32 = requireLib$g();
	var ldexp = requireLib$M();
	var LN2 = requireLib$f();
	var BIAS = requireLib$1l();
	var ABS_MASK = requireLib$1f();
	var HIGH_SIGNIFICAND_MASK = requireLib$e();
	var polyvalP = requirePolyval_p$1();


	// VARIABLES //

	// 0x00100000 = 1048576 => 0 00000000001 00000000000000000000 => biased exponent: 1 = -1022+1023 => 2^-1022
	var HIGH_MIN_NORMAL_EXP = 0x00100000|0; // asm type annotation

	// 0x3fe00000 = 1071644672 => 0 01111111110 00000000000000000000 => biased exponent: 1022 = -1+1023 => 2^-1
	var HIGH_BIASED_EXP_NEG_1 = 0x3fe00000|0; // asm type annotation

	// TODO: consider making into an external constant
	var HIGH_NUM_SIGNIFICAND_BITS = 20|0; // asm type annotation

	// High: LN2
	var LN2_HI = 6.93147182464599609375e-01; // 0x3FE62E43, 0x00000000

	// Low: LN2
	var LN2_LO = -1.90465429995776804525e-09; // 0xBE205C61, 0x0CA86C39


	// MAIN //

	/**
	* Computes \\(2^{\mathrm{hp} + \mathrm{lp}\\).
	*
	* @private
	* @param {number} j - high word of `hp + lp`
	* @param {number} hp - first power summand
	* @param {number} lp - second power summand
	* @returns {number} function value
	*
	* @example
	* var z = pow2( 1065961648, -0.3398475646972656, -0.000002438187359100815 );
	* // returns ~0.79
	*/
	function pow2( j, hp, lp ) {
		var tmp;
		var t1;
		var t;
		var r;
		var u;
		var v;
		var w;
		var z;
		var n;
		var i;
		var k;

		i = (j & ABS_MASK)|0; // asm type annotation
		k = ((i>>HIGH_NUM_SIGNIFICAND_BITS) - BIAS)|0; // asm type annotation
		n = 0;

		// `|z| > 0.5`, set `n = z+0.5`
		if ( i > HIGH_BIASED_EXP_NEG_1 ) {
			n = (j + (HIGH_MIN_NORMAL_EXP>>(k+1)))>>>0; // asm type annotation
			k = (((n & ABS_MASK)>>HIGH_NUM_SIGNIFICAND_BITS) - BIAS)|0; // new k for n
			tmp = ((n & ~(HIGH_SIGNIFICAND_MASK >> k)))>>>0; // asm type annotation
			t = setHighWord( 0.0, tmp );
			n = (((n & HIGH_SIGNIFICAND_MASK)|HIGH_MIN_NORMAL_EXP) >> (HIGH_NUM_SIGNIFICAND_BITS-k))>>>0; // eslint-disable-line max-len
			if ( j < 0 ) {
				n = -n;
			}
			hp -= t;
		}
		t = lp + hp;
		t = setLowWord( t, 0 );
		u = t * LN2_HI;
		v = ( (lp - (t-hp))*LN2 ) + ( t*LN2_LO );
		z = u + v;
		w = v - (z - u);
		t = z * z;
		t1 = z - ( t*polyvalP( t ) );
		r = ( (z*t1) / (t1-2.0) ) - ( w + (z*w) );
		z = 1.0 - (r - z);
		j = getHighWord( z );
		j = uint32ToInt32( j );
		j += (n << HIGH_NUM_SIGNIFICAND_BITS)>>>0; // asm type annotation

		// Check for subnormal output...
		if ( (j>>HIGH_NUM_SIGNIFICAND_BITS) <= 0 ) {
			z = ldexp( z, n );
		} else {
			z = setHighWord( z, j );
		}
		return z;
	}


	// EXPORTS //

	pow2_1$1 = pow2;
	return pow2_1$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var main$b;
var hasRequiredMain$b;

function requireMain$b () {
	if (hasRequiredMain$b) return main$b;
	hasRequiredMain$b = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var isOdd = requireLib$h();
	var isInfinite = requireLib$1h();
	var isInteger = requireLib$j();
	var sqrt = requireLib$I();
	var abs = requireLib$Q();
	var toWords = requireLib$V();
	var setLowWord = requireLib$K();
	var uint32ToInt32 = requireLib$g();
	var NINF = requireLib$1n();
	var PINF = requireLib$1m();
	var ABS_MASK = requireLib$1f();
	var xIsZero = requireX_is_zero$1();
	var yIsHuge = requireY_is_huge$1();
	var yIsInfinite = requireY_is_infinite$1();
	var log2ax = requireLog2ax$1();
	var logx = requireLogx$1();
	var pow2 = requirePow2$1();


	// VARIABLES //

	// 0x3fefffff = 1072693247 => 0 01111111110 11111111111111111111 => biased exponent: 1022 = -1+1023 => 2^-1
	var HIGH_MAX_NEAR_UNITY = 0x3fefffff|0; // asm type annotation

	// 0x41e00000 = 1105199104 => 0 10000011110 00000000000000000000 => biased exponent: 1054 = 31+1023 => 2^31
	var HIGH_BIASED_EXP_31 = 0x41e00000|0; // asm type annotation

	// 0x43f00000 = 1139802112 => 0 10000111111 00000000000000000000 => biased exponent: 1087 = 64+1023 => 2^64
	var HIGH_BIASED_EXP_64 = 0x43f00000|0; // asm type annotation

	// 0x40900000 = 1083179008 => 0 10000001001 00000000000000000000 => biased exponent: 1033 = 10+1023 => 2^10 = 1024
	var HIGH_BIASED_EXP_10 = 0x40900000|0; // asm type annotation

	// 0x3ff00000 = 1072693248 => 0 01111111111 00000000000000000000 => biased exponent: 1023 = 0+1023 => 2^0 = 1
	var HIGH_BIASED_EXP_0 = 0x3ff00000|0; // asm type annotation

	// 0x4090cc00 = 1083231232 => 0 10000001001 00001100110000000000
	var HIGH_1075 = 0x4090cc00|0; // asm type annotation

	// 0xc090cc00 = 3230714880 => 1 10000001001 00001100110000000000
	var HIGH_NEG_1075 = 0xc090cc00>>>0; // asm type annotation

	var HIGH_NUM_NONSIGN_BITS = 31|0; // asm type annotation

	var HUGE = 1.0e300;
	var TINY = 1.0e-300;

	// -(1024-log2(ovfl+.5ulp))
	var OVT = 8.0085662595372944372e-17;

	// High/low words workspace:
	var WORDS = [ 0|0, 0|0 ];

	// Log workspace:
	var LOG_WORKSPACE = [ 0.0, 0.0 ];


	// MAIN //

	/**
	* Evaluates the exponential function.
	*
	* ## Method
	*
	* 1.  Let \\(x = 2^n (1+f)\\).
	*
	* 2.  Compute \\(\operatorname{log2}(x)\\) as
	*
	*     ```tex
	*     \operatorname{log2}(x) = w_1 + w_2
	*     ```
	*
	*     where \\(w_1\\) has \\(53 - 24 = 29\\) bit trailing zeros.
	*
	* 3.  Compute
	*
	*     ```tex
	*     y \cdot \operatorname{log2}(x) = n + y^\prime
	*     ```
	*
	*     by simulating multi-precision arithmetic, where \\(|y^\prime| \leq 0.5\\).
	*
	* 4.  Return
	*
	*     ```tex
	*     x^y = 2^n e^{y^\prime \cdot \mathrm{log2}}
	*     ```
	*
	* ## Special Cases
	*
	* ```tex
	* \begin{align*}
	* x^{\mathrm{NaN}} &= \mathrm{NaN} & \\
	* (\mathrm{NaN})^y &= \mathrm{NaN} & \\
	* 1^y &= 1 & \\
	* x^0 &= 1 & \\
	* x^1 &= x & \\
	* (\pm 0)^\infty &= +0 & \\
	* (\pm 0)^{-\infty} &= +\infty & \\
	* (+0)^y &= +0 & \mathrm{if}\ y > 0 \\
	* (+0)^y &= +\infty & \mathrm{if}\ y < 0 \\
	* (-0)^y &= -\infty & \mathrm{if}\ y\ \mathrm{is\ an\ odd\ integer\ and}\ y < 0 \\
	* (-0)^y &= +\infty & \mathrm{if}\ y\ \mathrm{is\ not\ an\ odd\ integer\ and}\ y < 0 \\
	* (-0)^y &= -0 & \mathrm{if}\ y\ \mathrm{is\ an\ odd\ integer\ and}\ y > 0 \\
	* (-0)^y &= +0 & \mathrm{if}\ y\ \mathrm{is\ not\ an\ odd\ integer\ and}\ y > 0 \\
	* (-1)^{\pm\infty} &= \mathrm{NaN} & \\
	* x^{\infty} &= +\infty & |x| > 1 \\
	* x^{\infty} &= +0 & |x| < 1 \\
	* x^{-\infty} &= +0 & |x| > 1 \\
	* x^{-\infty} &= +\infty & |x| < 1 \\
	* (-\infty)^y &= (-0)^y & \\
	* \infty^y &= +0 & y < 0 \\
	* \infty^y &= +\infty & y > 0 \\
	* x^y &= \mathrm{NaN} & \mathrm{if}\ y\ \mathrm{is\ not\ a\ finite\ integer\ and}\ x < 0
	* \end{align*}
	* ```
	*
	* ## Notes
	*
	* -   \\(\operatorname{pow}(x,y)\\) returns \\(x^y\\) nearly rounded. In particular, \\(\operatorname{pow}(<\mathrm{integer}>,<\mathrm{integer}>)\\) **always** returns the correct integer, provided the value is representable.
	* -   The hexadecimal values shown in the source code are the intended values for used constants. Decimal values may be used, provided the compiler will accurately convert decimal to binary in order to produce the hexadecimal values.
	*
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} function value
	*
	* @example
	* var v = pow( 2.0, 3.0 );
	* // returns 8.0
	*
	* @example
	* var v = pow( 4.0, 0.5 );
	* // returns 2.0
	*
	* @example
	* var v = pow( 100.0, 0.0 );
	* // returns 1.0
	*
	* @example
	* var v = pow( 3.141592653589793, 5.0 );
	* // returns ~306.0197
	*
	* @example
	* var v = pow( 3.141592653589793, -0.2 );
	* // returns ~0.7954
	*
	* @example
	* var v = pow( NaN, 3.0 );
	* // returns NaN
	*
	* @example
	* var v = pow( 5.0, NaN );
	* // returns NaN
	*
	* @example
	* var v = pow( NaN, NaN );
	* // returns NaN
	*/
	function pow( x, y ) {
		var ahx; // absolute value high word `x`
		var ahy; // absolute value high word `y`
		var ax;  // absolute value `x`
		var hx;  // high word `x`
		var lx;  // low word `x`
		var hy;  // high word `y`
		var ly;  // low word `y`
		var sx;  // sign `x`
		var sy;  // sign `y`
		var y1;
		var hp;
		var lp;
		var t;
		var z;   // y prime
		var j;
		var i;
		if ( isnan( x ) || isnan( y ) ) {
			return NaN;
		}
		// Split `y` into high and low words:
		toWords.assign( y, WORDS, 1, 0 );
		hy = WORDS[ 0 ];
		ly = WORDS[ 1 ];

		// Special cases `y`...
		if ( ly === 0 ) {
			if ( y === 0.0 ) {
				return 1.0;
			}
			if ( y === 1.0 ) {
				return x;
			}
			if ( y === -1.0 ) {
				return 1.0 / x;
			}
			if ( y === 0.5 ) {
				return sqrt( x );
			}
			if ( y === -0.5 ) {
				return 1.0 / sqrt( x );
			}
			if ( y === 2.0 ) {
				return x * x;
			}
			if ( y === 3.0 ) {
				return x * x * x;
			}
			if ( y === 4.0 ) {
				x *= x;
				return x * x;
			}
			if ( isInfinite( y ) ) {
				return yIsInfinite( x, y );
			}
		}
		// Split `x` into high and low words:
		toWords.assign( x, WORDS, 1, 0 );
		hx = WORDS[ 0 ];
		lx = WORDS[ 1 ];

		// Special cases `x`...
		if ( lx === 0 ) {
			if ( hx === 0 ) {
				return xIsZero( x, y );
			}
			if ( x === 1.0 ) {
				return 1.0;
			}
			if (
				x === -1.0 &&
				isOdd( y )
			) {
				return -1.0;
			}
			if ( isInfinite( x ) ) {
				if ( x === NINF ) {
					// `pow( 1/x, -y )`
					return pow( -0.0, -y );
				}
				if ( y < 0.0 ) {
					return 0.0;
				}
				return PINF;
			}
		}
		if (
			x < 0.0 &&
			isInteger( y ) === false
		) {
			// Signal NaN...
			return (x-x)/(x-x);
		}
		ax = abs( x );

		// Remove the sign bits (i.e., get absolute values):
		ahx = (hx & ABS_MASK)|0; // asm type annotation
		ahy = (hy & ABS_MASK)|0; // asm type annotation

		// Extract the sign bits:
		sx = (hx >>> HIGH_NUM_NONSIGN_BITS)|0; // asm type annotation
		sy = (hy >>> HIGH_NUM_NONSIGN_BITS)|0; // asm type annotation

		// Determine the sign of the result...
		if ( sx && isOdd( y ) ) {
			sx = -1.0;
		} else {
			sx = 1.0;
		}
		// Case 1: `|y|` is huge...

		// |y| > 2^31
		if ( ahy > HIGH_BIASED_EXP_31 ) {
			// `|y| > 2^64`, then must over- or underflow...
			if ( ahy > HIGH_BIASED_EXP_64 ) {
				return yIsHuge( x, y );
			}
			// Over- or underflow if `x` is not close to unity...

			if ( ahx < HIGH_MAX_NEAR_UNITY ) {
				// y < 0
				if ( sy === 1 ) {
					// Signal overflow...
					return sx * HUGE * HUGE;
				}
				// Signal underflow...
				return sx * TINY * TINY;
			}
			if ( ahx > HIGH_BIASED_EXP_0 ) {
				// y > 0
				if ( sy === 0 ) {
					// Signal overflow...
					return sx * HUGE * HUGE;
				}
				// Signal underflow...
				return sx * TINY * TINY;
			}
			// At this point, `|1-x|` is tiny (`<= 2^-20`). Suffice to compute `log(x)` by `x - x^2/2 + x^3/3 - x^4/4`.
			t = logx( LOG_WORKSPACE, ax );
		}
		// Case 2: `|y|` is not huge...
		else {
			t = log2ax( LOG_WORKSPACE, ax, ahx );
		}
		// Split `y` into `y1 + y2` and compute `(y1+y2) * (t1+t2)`...
		y1 = setLowWord( y, 0 );
		lp = ( (y-y1)*t[0] ) + ( y*t[1] );
		hp = y1 * t[0];
		z = lp + hp;

		// Note: *can* be more performant to use `getHighWord` and `getLowWord` directly, but using `toWords` looks cleaner.
		toWords.assign( z, WORDS, 1, 0 );
		j = uint32ToInt32( WORDS[0] );
		i = uint32ToInt32( WORDS[1] );

		// z >= 1024
		if ( j >= HIGH_BIASED_EXP_10 ) {
			// z > 1024
			if ( ((j-HIGH_BIASED_EXP_10)|i) !== 0 ) {
				// Signal overflow...
				return sx * HUGE * HUGE;
			}
			if ( (lp+OVT) > (z-hp) ) {
				// Signal overflow...
				return sx * HUGE * HUGE;
			}
		}
		// z <= -1075
		else if ( (j&ABS_MASK) >= HIGH_1075 ) {
			// z < -1075
			if ( ((j-HIGH_NEG_1075)|i) !== 0 ) {
				// Signal underflow...
				return sx * TINY * TINY;
			}
			if ( lp <= (z-hp) ) {
				// Signal underflow...
				return sx * TINY * TINY;
			}
		}
		// Compute `2^(hp+lp)`...
		z = pow2( j, hp, lp );

		return sx * z;
	}


	// EXPORTS //

	main$b = pow;
	return main$b;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$d;
var hasRequiredLib$d;

function requireLib$d () {
	if (hasRequiredLib$d) return lib$d;
	hasRequiredLib$d = 1;

	/**
	* Evaluate the exponential function.
	*
	* @module @stdlib/math-base-special-pow
	*
	* @example
	* var pow = require( '@stdlib/math-base-special-pow' );
	*
	* var v = pow( 2.0, 3.0 );
	* // returns 8.0
	*
	* v = pow( 4.0, 0.5 );
	* // returns 2.0
	*
	* v = pow( 100.0, 0.0 );
	* // returns 1.0
	*
	* v = pow( 3.141592653589793, 5.0 );
	* // returns ~306.0197
	*
	* v = pow( 3.141592653589793, -0.2 );
	* // returns ~0.7954
	*
	* v = pow( NaN, 3.0 );
	* // returns NaN
	*
	* v = pow( 5.0, NaN );
	* // returns NaN
	*
	* v = pow( NaN, NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$b();


	// EXPORTS //

	lib$d = main;
	return lib$d;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$c;
var hasRequiredLib$c;

function requireLib$c () {
	if (hasRequiredLib$c) return lib$c;
	hasRequiredLib$c = 1;

	/**
	* Natural logarithm of `2π`.
	*
	* @module @stdlib/constants-float64-ln-two-pi
	* @type {number}
	*
	* @example
	* var LN_TWO_PI = require( '@stdlib/constants-float64-ln-two-pi' );
	* // returns 1.8378770664093456
	*/


	// MAIN //

	/**
	* Natural logarithm of `2π`.
	*
	* ```tex
	* \ln 2\pi
	* ```
	*
	* @constant
	* @type {number}
	* @default 1.8378770664093456
	*/
	var LN_TWO_PI = 1.837877066409345483560659472811235279722794947275566825634;


	// EXPORTS //

	lib$c = LN_TWO_PI;
	return lib$c;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$a;
var hasRequiredMain$a;

function requireMain$a () {
	if (hasRequiredMain$a) return main$a;
	hasRequiredMain$a = 1;

	// MODULES //

	var ln = requireLib$z();
	var pow = requireLib$d();
	var LN_TWO_PI = requireLib$c();
	var NINF = requireLib$1n();
	var PINF = requireLib$1m();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the natural logarithm of the probability density function (PDF) for a normal distribution with mean `mu` and standard deviation `sigma` at a value `x`.
	*
	* @param {number} x - input value
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {number} logarithm of probability density function
	*
	* @example
	* var y = logpdf( 2.0, 0.0, 1.0 );
	* // returns ~-2.919
	*
	* @example
	* var y = logpdf( -1.0, 4.0, 2.0 );
	* // returns ~-4.737
	*
	* @example
	* var y = logpdf( NaN, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = logpdf( 0.0, NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = logpdf( 0.0, 0.0, NaN );
	* // returns NaN
	*
	* @example
	* // Negative standard deviation:
	* var y = logpdf( 2.0, 0.0, -1.0 );
	* // returns NaN
	*
	* @example
	* var y = logpdf( 2.0, 8.0, 0.0 );
	* // returns -Infinity
	*
	* @example
	* var y = logpdf( 8.0, 8.0, 0.0 );
	* // returns Infinity
	*/
	function logpdf( x, mu, sigma ) {
		var s2;
		var A;
		var B;
		if (
			isnan( x ) ||
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return NaN;
		}
		if ( sigma === 0.0 ) {
			return ( x === mu ) ? PINF : NINF;
		}
		s2 = pow( sigma, 2.0 );
		A = (-0.5) * ( ( 2.0*ln( sigma ) ) + LN_TWO_PI );
		B = -1.0 / ( 2.0*s2 );
		return A + ( B * pow( x-mu, 2.0 ) );
	}


	// EXPORTS //

	main$a = logpdf;
	return main$a;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$9;
var hasRequiredMain$9;

function requireMain$9 () {
	if (hasRequiredMain$9) return main$9;
	hasRequiredMain$9 = 1;

	// MODULES //

	var PINF = requireLib$1m();
	var NINF = requireLib$1n();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the natural logarithm of the probability density function (logPDF) for a degenerate distribution centered at `mu`.
	*
	* @param {number} x - input value
	* @param {number} mu - constant value of the distribution
	* @returns {number} natural logarithm of probability density function
	*
	* @example
	* var y = logpdf( 2.0, 3.0 );
	* // returns -Infinity
	*
	* @example
	* var y = logpdf( 3.0, 3.0 );
	* // returns Infinity
	*
	* @example
	* var y = logpdf( NaN, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = logpdf( 0.0, NaN );
	* // returns NaN
	*/
	function logpdf( x, mu ) {
		if ( isnan( x ) || isnan( mu ) ) {
			return NaN;
		}
		return ( x === mu ) ? PINF : NINF;
	}


	// EXPORTS //

	main$9 = logpdf;
	return main$9;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$6;
var hasRequiredFactory$6;

function requireFactory$6 () {
	if (hasRequiredFactory$6) return factory_1$6;
	hasRequiredFactory$6 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var PINF = requireLib$1m();
	var NINF = requireLib$1n();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns a function for evaluating the natural logarithm of the probability density function (logPDF) of a degenerate distribution centered at a provided mean value.
	*
	* @param {number} mu - value at which to center the distribution
	* @returns {Function} function to evaluate the natural logarithm of the probability density function
	*
	* @example
	* var logpdf = factory( 5.0 );
	*
	* var y = logpdf( 0.0 );
	* // returns -Infinity
	*
	* y = logpdf( 5.0 );
	* // returns Infinity
	*/
	function factory( mu ) {
		if ( isnan( mu ) ) {
			return constantFunction( NaN );
		}
		return logpdf;

		/**
		* Evaluates the natural logarithm of the probability density function (logPDF) of a degenerate distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {number} natural logarithm of the probability density function
		*
		* @example
		* var y = logpdf( 10.0 );
		* // returns <number>
		*/
		function logpdf( x ) {
			if ( isnan( x ) ) {
				return NaN;
			}
			return ( x === mu ) ? PINF : NINF;
		}
	}


	// EXPORTS //

	factory_1$6 = factory;
	return factory_1$6;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$b;
var hasRequiredLib$b;

function requireLib$b () {
	if (hasRequiredLib$b) return lib$b;
	hasRequiredLib$b = 1;

	/**
	* Degenerate distribution logarithm of probability density function (logPDF).
	*
	* @module @stdlib/stats-base-dists-degenerate-logpdf
	*
	* @example
	* var logpdf = require( '@stdlib/stats-base-dists-degenerate-logpdf' );
	*
	* var y = logpdf( 2.0, 0.0 );
	* // returns -Infinity
	*
	* @example
	* var factory = require( '@stdlib/stats-base-dists-degenerate-logpdf' ).factory;
	*
	* var logPDF = factory( 10.0 );
	*
	* var y = logPDF( 10.0 );
	* // returns Infinity
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$9();
	var factory = requireFactory$6();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$b = main;
	return lib$b;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$5;
var hasRequiredFactory$5;

function requireFactory$5 () {
	if (hasRequiredFactory$5) return factory_1$5;
	hasRequiredFactory$5 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var degenerate = requireLib$b().factory;
	var LN_TWO_PI = requireLib$c();
	var isnan = requireLib$1s();
	var pow = requireLib$d();
	var ln = requireLib$z();


	// MAIN //

	/**
	* Returns a function for evaluating the natural logarithm of the probability density function (PDF) for a normal distribution.
	*
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {Function} logPDF
	*
	* @example
	* var logpdf = factory( 10.0, 2.0 );
	* var y = logpdf( 10.0 );
	* // returns ~-1.612
	*
	* y = logpdf( 5.0 );
	* // returns ~-4.737
	*/
	function factory( mu, sigma ) {
		var s2;
		var A;
		var B;
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return constantFunction( NaN );
		}
		if ( sigma === 0.0 ) {
			return degenerate( mu );
		}
		s2 = pow( sigma, 2.0 );
		A = (-0.5) * ( ( 2.0*ln( sigma ) ) + LN_TWO_PI );
		B = -1.0 / ( 2.0*s2 );
		return logpdf;

		/**
		* Evaluates the natural logarithm of the probability density function (PDF) for a normal distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {number} evaluated logPDF
		*
		* @example
		* var y = logpdf( -3.14 );
		* // returns <number>
		*/
		function logpdf( x ) {
			return A + ( B * pow( x-mu, 2.0 ) );
		}
	}


	// EXPORTS //

	factory_1$5 = factory;
	return factory_1$5;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$a;
var hasRequiredLib$a;

function requireLib$a () {
	if (hasRequiredLib$a) return lib$a;
	hasRequiredLib$a = 1;

	/**
	* Natural logarithm of the probability density function (PDF) for a normal distribution.
	*
	* @module @stdlib/stats-base-dists-normal-logpdf
	*
	* @example
	* var logpdf = require( '@stdlib/stats-base-dists-normal-logpdf' );
	*
	* var y = logpdf( 2.0, 0.0, 1.0 );
	* // returns ~-2.919
	*
	* var myLogPDF = logpdf.factory( 10.0, 2.0 );
	* y = myLogPDF( 10.0 );
	* // returns ~-1.612
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$a();
	var factory = requireFactory$5();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$a = main;
	return lib$a;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$8;
var hasRequiredMain$8;

function requireMain$8 () {
	if (hasRequiredMain$8) return main$8;
	hasRequiredMain$8 = 1;

	// MODULES //

	var isEven = requireLib$i();


	// MAIN //

	/**
	* Tests if a finite double-precision floating-point number is an odd number.
	*
	* @param {number} x - value to test
	* @returns {boolean} boolean indicating whether the value is an odd number
	*
	* @example
	* var bool = isOdd( 5.0 );
	* // returns true
	*
	* @example
	* var bool = isOdd( -2.0 );
	* // returns false
	*
	* @example
	* var bool = isOdd( 0.0 );
	* // returns false
	*
	* @example
	* var bool = isOdd( NaN );
	* // returns false
	*/
	function isOdd( x ) {
		// Check sign to prevent overflow...
		if ( x > 0.0 ) {
			return isEven( x-1.0 );
		}
		return isEven( x+1.0 );
	}


	// EXPORTS //

	main$8 = isOdd;
	return main$8;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$9;
var hasRequiredLib$9;

function requireLib$9 () {
	if (hasRequiredLib$9) return lib$9;
	hasRequiredLib$9 = 1;

	/**
	* Test if a finite double-precision floating-point number is an odd number.
	*
	* @module @stdlib/math-base-assert-is-odd
	*
	* @example
	* var isOdd = require( '@stdlib/math-base-assert-is-odd' );
	*
	* var bool = isOdd( 5.0 );
	* // returns true
	*
	* bool = isOdd( -2.0 );
	* // returns false
	*
	* bool = isOdd( 0.0 );
	* // returns false
	*
	* bool = isOdd( NaN );
	* // returns false
	*/

	// MODULES //

	var main = requireMain$8();


	// EXPORTS //

	lib$9 = main;
	return lib$9;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var x_is_zero;
var hasRequiredX_is_zero;

function requireX_is_zero () {
	if (hasRequiredX_is_zero) return x_is_zero;
	hasRequiredX_is_zero = 1;

	// MODULES //

	var isOdd = requireLib$9();
	var copysign = requireLib$S();
	var NINF = requireLib$1n();
	var PINF = requireLib$1m();


	// MAIN //

	/**
	* Evaluates the exponential function when \\(|x| = 0\\).
	*
	* @private
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} function value
	*
	* @example
	* var v = pow( 0.0, 2 );
	* // returns 0.0
	*
	* @example
	* var v = pow( -0.0, -9 );
	* // returns -Infinity
	*
	* @example
	* var v = pow( 0.0, -9 );
	* // returns Infinity
	*
	* @example
	* var v = pow( -0.0, 9 );
	* // returns 0.0
	*
	* @example
	* var v = pow( 0.0, -Infinity  );
	* // returns Infinity
	*
	* @example
	* var v = pow( 0.0, Infinity );
	* // returns 0.0
	*/
	function pow( x, y ) {
		if ( y === NINF ) {
			return PINF;
		}
		if ( y === PINF ) {
			return 0.0;
		}
		if ( y > 0.0 ) {
			if ( isOdd( y ) ) {
				return x; // handles +-0
			}
			return 0.0;
		}
		// y < 0.0
		if ( isOdd( y ) ) {
			return copysign( PINF, x ); // handles +-0
		}
		return PINF;
	}


	// EXPORTS //

	x_is_zero = pow;
	return x_is_zero;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var y_is_huge;
var hasRequiredY_is_huge;

function requireY_is_huge () {
	if (hasRequiredY_is_huge) return y_is_huge;
	hasRequiredY_is_huge = 1;

	// MODULES //

	var ABS_MASK = requireLib$1f();
	var getHighWord = requireLib$U();


	// VARIABLES //

	// 0x3fefffff = 1072693247 => 0 01111111110 11111111111111111111 => biased exponent: 1022 = -1+1023 => 2^-1
	var HIGH_MAX_NEAR_UNITY = 0x3fefffff|0; // asm type annotation

	var HUGE = 1.0e300;
	var TINY = 1.0e-300;


	// MAIN //

	/**
	* Evaluates the exponential function when \\(|y| > 2^64\\).
	*
	* @private
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} overflow or underflow result
	*
	* @example
	* var v = pow( 9.0, 3.6893488147419103e19 );
	* // returns Infinity
	*
	* @example
	* var v = pow( -3.14, -3.6893488147419103e19 );
	* // returns 0.0
	*/
	function pow( x, y ) {
		var ahx;
		var hx;

		hx = getHighWord( x );
		ahx = (hx & ABS_MASK);

		if ( ahx <= HIGH_MAX_NEAR_UNITY ) {
			if ( y < 0 ) {
				// Signal overflow...
				return HUGE * HUGE;
			}
			// Signal underflow...
			return TINY * TINY;
		}
		// `x` has a biased exponent greater than or equal to `0`...

		if ( y > 0 ) {
			// Signal overflow...
			return HUGE * HUGE;
		}
		// Signal underflow...
		return TINY * TINY;
	}


	// EXPORTS //

	y_is_huge = pow;
	return y_is_huge;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var y_is_infinite;
var hasRequiredY_is_infinite;

function requireY_is_infinite () {
	if (hasRequiredY_is_infinite) return y_is_infinite;
	hasRequiredY_is_infinite = 1;

	// MODULES //

	var abs = requireLib$Q();
	var PINF = requireLib$1m();


	// MAIN //

	/**
	* Evaluates the exponential function when \\( y = \pm \infty\\).
	*
	* @private
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} function value
	*
	* @example
	* var v = pow( -1.0, Infinity );
	* // returns NaN
	*
	* @example
	* var v = pow( -1.0, -Infinity  );
	* // returns NaN
	*
	* @example
	* var v = pow( 1.0, Infinity );
	* // returns 1.0
	*
	* @example
	* var v = pow( 1.0, -Infinity  );
	* // returns 1.0
	*
	* @example
	* var v = pow( 0.5, Infinity );
	* // returns 0.0
	*
	* @example
	* var v = pow( 0.5, -Infinity  );
	* // returns Infinity
	*
	* @example
	* var v = pow( 1.5, -Infinity  );
	* // returns 0.0
	*
	* @example
	* var v = pow( 1.5, Infinity );
	* // returns Infinity
	*/
	function pow( x, y ) {
		if ( x === -1.0 ) {
			// Julia (0.4.2) and Python (2.7.9) return `1.0` (WTF???). JavaScript (`Math.pow`), R, and libm return `NaN`. We choose `NaN`, as the value is indeterminate; i.e., we cannot determine whether `y` is odd, even, or somewhere in between.
			return (x-x)/(x-x); // signal NaN
		}
		if ( x === 1.0 ) {
			return 1.0;
		}
		// (|x| > 1 && y === NINF) || (|x| < 1 && y === PINF)
		if ( (abs(x) < 1.0) === (y === PINF) ) {
			return 0.0;
		}
		// (|x| > 1 && y === PINF) || (|x| < 1 && y === NINF)
		return PINF;
	}


	// EXPORTS //

	y_is_infinite = pow;
	return y_is_infinite;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_l;
var hasRequiredPolyval_l;

function requirePolyval_l () {
	if (hasRequiredPolyval_l) return polyval_l;
	hasRequiredPolyval_l = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.5999999999999946;
		}
		return 0.5999999999999946 + (x * (0.4285714285785502 + (x * (0.33333332981837743 + (x * (0.272728123808534 + (x * (0.23066074577556175 + (x * 0.20697501780033842))))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_l = evalpoly;
	return polyval_l;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var log2ax_1;
var hasRequiredLog2ax;

function requireLog2ax () {
	if (hasRequiredLog2ax) return log2ax_1;
	hasRequiredLog2ax = 1;

	// MODULES //

	var getHighWord = requireLib$U();
	var setLowWord = requireLib$K();
	var setHighWord = requireLib$A();
	var BIAS = requireLib$1l();
	var polyvalL = requirePolyval_l();


	// VARIABLES //

	// 0x000fffff = 1048575 => 0 00000000000 11111111111111111111
	var HIGH_SIGNIFICAND_MASK = 0x000fffff|0; // asm type annotation

	// 0x00100000 = 1048576 => 0 00000000001 00000000000000000000 => biased exponent: 1 = -1022+1023 => 2^-1022
	var HIGH_MIN_NORMAL_EXP = 0x00100000|0; // asm type annotation

	// 0x3ff00000 = 1072693248 => 0 01111111111 00000000000000000000 => biased exponent: 1023 = 0+1023 => 2^0 = 1
	var HIGH_BIASED_EXP_0 = 0x3ff00000|0; // asm type annotation

	// 0x20000000 = 536870912 => 0 01000000000 00000000000000000000 => biased exponent: 512 = -511+1023
	var HIGH_BIASED_EXP_NEG_512 = 0x20000000|0; // asm type annotation

	// 0x00080000 = 524288 => 0 00000000000 10000000000000000000
	var HIGH_SIGNIFICAND_HALF = 0x00080000|0; // asm type annotation

	// TODO: consider making an external constant
	var HIGH_NUM_SIGNIFICAND_BITS = 20|0; // asm type annotation

	var TWO53 = 9007199254740992.0;	// 0x43400000, 0x00000000

	// 2/(3*LN2)
	var CP = 9.61796693925975554329e-01; // 0x3FEEC709, 0xDC3A03FD

	// (float)CP
	var CP_HI = 9.61796700954437255859e-01; // 0x3FEEC709, 0xE0000000

	// Low: CP_HI
	var CP_LO = -7.02846165095275826516e-09; // 0xBE3E2FE0, 0x145B01F5

	var BP = [
		1.0,
		1.5
	];
	var DP_HI = [
		0.0,
		5.84962487220764160156e-01 // 0x3FE2B803, 0x40000000
	];
	var DP_LO = [
		0.0,
		1.35003920212974897128e-08 // 0x3E4CFDEB, 0x43CFD006
	];


	// MAIN //

	/**
	* Computes \\(\operatorname{log2}(ax)\\).
	*
	* @private
	* @param {Array} out - output array
	* @param {number} ax - absolute value of `x`
	* @param {number} ahx - high word of `ax`
	* @returns {Array} output array containing a tuple comprised of high and low parts
	*
	* @example
	* var t = log2ax( [ 0.0, 0.0 ], 9.0, 1075970048 ); // => [ t1, t2 ]
	* // returns [ 3.169923782348633, 0.0000012190936795504075 ]
	*/
	function log2ax( out, ax, ahx ) {
		var tmp;
		var ss; // `hs + ls`
		var s2; // `ss` squared
		var hs;
		var ls;
		var ht;
		var lt;
		var bp; // `BP` constant
		var dp; // `DP` constant
		var hp;
		var lp;
		var hz;
		var lz;
		var t1;
		var t2;
		var t;
		var r;
		var u;
		var v;
		var n;
		var j;
		var k;

		n = 0|0; // asm type annotation

		// Check if `x` is subnormal...
		if ( ahx < HIGH_MIN_NORMAL_EXP ) {
			ax *= TWO53;
			n -= 53|0; // asm type annotation
			ahx = getHighWord( ax );
		}
		// Extract the unbiased exponent of `x`:
		n += ((ahx >> HIGH_NUM_SIGNIFICAND_BITS) - BIAS)|0; // asm type annotation

		// Isolate the significand bits of `x`:
		j = (ahx & HIGH_SIGNIFICAND_MASK)|0; // asm type annotation

		// Normalize `ahx` by setting the (biased) exponent to `1023`:
		ahx = (j | HIGH_BIASED_EXP_0)|0; // asm type annotation

		// Determine the interval of `|x|` by comparing significand bits...

		// |x| < sqrt(3/2)
		if ( j <= 0x3988E ) { // 0 00000000000 00111001100010001110
			k = 0;
		}
		// |x| < sqrt(3)
		else if ( j < 0xBB67A ) { // 0 00000000000 10111011011001111010
			k = 1;
		}
		// |x| >= sqrt(3)
		else {
			k = 0;
			n += 1|0; // asm type annotation
			ahx -= HIGH_MIN_NORMAL_EXP;
		}
		// Load the normalized high word into `|x|`:
		ax = setHighWord( ax, ahx );

		// Compute `ss = hs + ls = (x-1)/(x+1)` or `(x-1.5)/(x+1.5)`:
		bp = BP[ k ]; // BP[0] = 1.0, BP[1] = 1.5
		u = ax - bp; // (x-1) || (x-1.5)
		v = 1.0 / (ax + bp); // 1/(x+1) || 1/(x+1.5)
		ss = u * v;
		hs = setLowWord( ss, 0 ); // set all low word (less significant significand) bits to 0s

		// Compute `ht = ax + bp` (via manipulation, i.e., bit flipping, of the high word):
		tmp = ((ahx>>1) | HIGH_BIASED_EXP_NEG_512) + HIGH_SIGNIFICAND_HALF;
		tmp += (k << 18); // `(k<<18)` can be considered the word equivalent of `1.0` or `1.5`
		ht = setHighWord( 0.0, tmp );
		lt = ax - (ht - bp);
		ls = v * ( ( u - (hs*ht) ) - ( hs*lt ) );

		// Compute `log(ax)`...

		s2 = ss * ss;
		r = s2 * s2 * polyvalL( s2 );
		r += ls * (hs + ss);
		s2 = hs * hs;
		ht = 3.0 + s2 + r;
		ht = setLowWord( ht, 0 );
		lt = r - ((ht-3.0) - s2);

		// u+v = ss*(1+...):
		u = hs * ht;
		v = ( ls*ht ) + ( lt*ss );

		// 2/(3LN2) * (ss+...):
		hp = u + v;
		hp = setLowWord( hp, 0 );
		lp = v - (hp - u);
		hz = CP_HI * hp; // CP_HI+CP_LO = 2/(3*LN2)
		lz = ( CP_LO*hp ) + ( lp*CP ) + DP_LO[ k ];

		// log2(ax) = (ss+...)*2/(3*LN2) = n + dp + hz + lz
		dp = DP_HI[ k ];
		t = n;
		t1 = ((hz+lz) + dp) + t; // log2(ax)
		t1 = setLowWord( t1, 0 );
		t2 = lz - (((t1-t) - dp) - hz);

		out[ 0 ] = t1;
		out[ 1 ] = t2;
		return out;
	}


	// EXPORTS //

	log2ax_1 = log2ax;
	return log2ax_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_w;
var hasRequiredPolyval_w;

function requirePolyval_w () {
	if (hasRequiredPolyval_w) return polyval_w;
	hasRequiredPolyval_w = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.5;
		}
		return 0.5 + (x * (-0.3333333333333333 + (x * 0.25)));
	}


	// EXPORTS //

	polyval_w = evalpoly;
	return polyval_w;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var logx_1;
var hasRequiredLogx;

function requireLogx () {
	if (hasRequiredLogx) return logx_1;
	hasRequiredLogx = 1;

	// MODULES //

	var setLowWord = requireLib$K();
	var polyvalW = requirePolyval_w();


	// VARIABLES //

	// 1/LN2
	var INV_LN2 = 1.44269504088896338700e+00; // 0x3FF71547, 0x652B82FE

	// High (24 bits): 1/LN2
	var INV_LN2_HI = 1.44269502162933349609e+00; // 0x3FF71547, 0x60000000

	// Low: 1/LN2
	var INV_LN2_LO = 1.92596299112661746887e-08; // 0x3E54AE0B, 0xF85DDF44


	// MAIN //

	/**
	* Computes \\(\operatorname{log}(x)\\) assuming \\(|1-x|\\) is small and using the approximation \\(x - x^2/2 + x^3/3 - x^4/4\\).
	*
	* @private
	* @param {Array} out - output array
	* @param {number} ax - absolute value of `x`
	* @returns {Array} output array containing a tuple comprised of high and low parts
	*
	* @example
	* var t = logx( [ 0.0, 0.0 ], 9.0 ); // => [ t1, t2 ]
	* // returns [ -1265.7236328125, -0.0008163940840404393 ]
	*/
	function logx( out, ax ) {
		var t2;
		var t1;
		var t;
		var w;
		var u;
		var v;

		t = ax - 1.0; // `t` has `20` trailing zeros
		w = t * t * polyvalW( t );
		u = INV_LN2_HI * t; // `INV_LN2_HI` has `21` significant bits
		v = ( t*INV_LN2_LO ) - ( w*INV_LN2 );
		t1 = u + v;
		t1 = setLowWord( t1, 0 );
		t2 = v - (t1 - u);

		out[ 0 ] = t1;
		out[ 1 ] = t2;
		return out;
	}


	// EXPORTS //

	logx_1 = logx;
	return logx_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var polyval_p;
var hasRequiredPolyval_p;

function requirePolyval_p () {
	if (hasRequiredPolyval_p) return polyval_p;
	hasRequiredPolyval_p = 1;

	// MAIN //

	/**
	* Evaluates a polynomial.
	*
	* ## Notes
	*
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the polynomial
	* @returns {number} evaluated polynomial
	*/
	function evalpoly( x ) {
		if ( x === 0.0 ) {
			return 0.16666666666666602;
		}
		return 0.16666666666666602 + (x * (-0.0027777777777015593 + (x * (0.00006613756321437934 + (x * (-0.0000016533902205465252 + (x * 4.1381367970572385e-8))))))); // eslint-disable-line max-len
	}


	// EXPORTS //

	polyval_p = evalpoly;
	return polyval_p;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var pow2_1;
var hasRequiredPow2;

function requirePow2 () {
	if (hasRequiredPow2) return pow2_1;
	hasRequiredPow2 = 1;

	// MODULES //

	var getHighWord = requireLib$U();
	var setHighWord = requireLib$A();
	var setLowWord = requireLib$K();
	var uint32ToInt32 = requireLib$g();
	var ldexp = requireLib$M();
	var LN2 = requireLib$f();
	var BIAS = requireLib$1l();
	var ABS_MASK = requireLib$1f();
	var HIGH_SIGNIFICAND_MASK = requireLib$e();
	var polyvalP = requirePolyval_p();


	// VARIABLES //

	// 0x00100000 = 1048576 => 0 00000000001 00000000000000000000 => biased exponent: 1 = -1022+1023 => 2^-1022
	var HIGH_MIN_NORMAL_EXP = 0x00100000|0; // asm type annotation

	// 0x3fe00000 = 1071644672 => 0 01111111110 00000000000000000000 => biased exponent: 1022 = -1+1023 => 2^-1
	var HIGH_BIASED_EXP_NEG_1 = 0x3fe00000|0; // asm type annotation

	// TODO: consider making into an external constant
	var HIGH_NUM_SIGNIFICAND_BITS = 20|0; // asm type annotation

	// High: LN2
	var LN2_HI = 6.93147182464599609375e-01; // 0x3FE62E43, 0x00000000

	// Low: LN2
	var LN2_LO = -1.90465429995776804525e-09; // 0xBE205C61, 0x0CA86C39


	// MAIN //

	/**
	* Computes \\(2^{\mathrm{hp} + \mathrm{lp}\\).
	*
	* @private
	* @param {number} j - high word of `hp + lp`
	* @param {number} hp - first power summand
	* @param {number} lp - second power summand
	* @returns {number} function value
	*
	* @example
	* var z = pow2( 1065961648, -0.3398475646972656, -0.000002438187359100815 );
	* // returns ~0.79
	*/
	function pow2( j, hp, lp ) {
		var tmp;
		var t1;
		var t;
		var r;
		var u;
		var v;
		var w;
		var z;
		var n;
		var i;
		var k;

		i = (j & ABS_MASK)|0; // asm type annotation
		k = ((i>>HIGH_NUM_SIGNIFICAND_BITS) - BIAS)|0; // asm type annotation
		n = 0;

		// `|z| > 0.5`, set `n = z+0.5`
		if ( i > HIGH_BIASED_EXP_NEG_1 ) {
			n = (j + (HIGH_MIN_NORMAL_EXP>>(k+1)))>>>0; // asm type annotation
			k = (((n & ABS_MASK)>>HIGH_NUM_SIGNIFICAND_BITS) - BIAS)|0; // new k for n
			tmp = ((n & ~(HIGH_SIGNIFICAND_MASK >> k)))>>>0; // asm type annotation
			t = setHighWord( 0.0, tmp );
			n = (((n & HIGH_SIGNIFICAND_MASK)|HIGH_MIN_NORMAL_EXP) >> (HIGH_NUM_SIGNIFICAND_BITS-k))>>>0; // eslint-disable-line max-len
			if ( j < 0 ) {
				n = -n;
			}
			hp -= t;
		}
		t = lp + hp;
		t = setLowWord( t, 0 );
		u = t * LN2_HI;
		v = ( (lp - (t-hp))*LN2 ) + ( t*LN2_LO );
		z = u + v;
		w = v - (z - u);
		t = z * z;
		t1 = z - ( t*polyvalP( t ) );
		r = ( (z*t1) / (t1-2.0) ) - ( w + (z*w) );
		z = 1.0 - (r - z);
		j = getHighWord( z );
		j = uint32ToInt32( j );
		j += (n << HIGH_NUM_SIGNIFICAND_BITS)>>>0; // asm type annotation

		// Check for subnormal output...
		if ( (j>>HIGH_NUM_SIGNIFICAND_BITS) <= 0 ) {
			z = ldexp( z, n );
		} else {
			z = setHighWord( z, j );
		}
		return z;
	}


	// EXPORTS //

	pow2_1 = pow2;
	return pow2_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The following copyright and license were part of the original implementation available as part of [FreeBSD]{@link https://svnweb.freebsd.org/base/release/9.3.0/lib/msun/src/s_pow.c}. The implementation follows the original, but has been modified for JavaScript.
*
* ```text
* Copyright (C) 2004 by Sun Microsystems, Inc. All rights reserved.
*
* Developed at SunPro, a Sun Microsystems, Inc. business.
* Permission to use, copy, modify, and distribute this
* software is freely granted, provided that this notice
* is preserved.
* ```
*/

var main$7;
var hasRequiredMain$7;

function requireMain$7 () {
	if (hasRequiredMain$7) return main$7;
	hasRequiredMain$7 = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var isOdd = requireLib$9();
	var isInfinite = requireLib$1h();
	var isInteger = requireLib$j();
	var sqrt = requireLib$I();
	var abs = requireLib$Q();
	var toWords = requireLib$V();
	var setLowWord = requireLib$K();
	var uint32ToInt32 = requireLib$g();
	var NINF = requireLib$1n();
	var PINF = requireLib$1m();
	var ABS_MASK = requireLib$1f();
	var xIsZero = requireX_is_zero();
	var yIsHuge = requireY_is_huge();
	var yIsInfinite = requireY_is_infinite();
	var log2ax = requireLog2ax();
	var logx = requireLogx();
	var pow2 = requirePow2();


	// VARIABLES //

	// 0x3fefffff = 1072693247 => 0 01111111110 11111111111111111111 => biased exponent: 1022 = -1+1023 => 2^-1
	var HIGH_MAX_NEAR_UNITY = 0x3fefffff|0; // asm type annotation

	// 0x41e00000 = 1105199104 => 0 10000011110 00000000000000000000 => biased exponent: 1054 = 31+1023 => 2^31
	var HIGH_BIASED_EXP_31 = 0x41e00000|0; // asm type annotation

	// 0x43f00000 = 1139802112 => 0 10000111111 00000000000000000000 => biased exponent: 1087 = 64+1023 => 2^64
	var HIGH_BIASED_EXP_64 = 0x43f00000|0; // asm type annotation

	// 0x40900000 = 1083179008 => 0 10000001001 00000000000000000000 => biased exponent: 1033 = 10+1023 => 2^10 = 1024
	var HIGH_BIASED_EXP_10 = 0x40900000|0; // asm type annotation

	// 0x3ff00000 = 1072693248 => 0 01111111111 00000000000000000000 => biased exponent: 1023 = 0+1023 => 2^0 = 1
	var HIGH_BIASED_EXP_0 = 0x3ff00000|0; // asm type annotation

	// 0x4090cc00 = 1083231232 => 0 10000001001 00001100110000000000
	var HIGH_1075 = 0x4090cc00|0; // asm type annotation

	// 0xc090cc00 = 3230714880 => 1 10000001001 00001100110000000000
	var HIGH_NEG_1075 = 0xc090cc00>>>0; // asm type annotation

	var HIGH_NUM_NONSIGN_BITS = 31|0; // asm type annotation

	var HUGE = 1.0e300;
	var TINY = 1.0e-300;

	// -(1024-log2(ovfl+.5ulp))
	var OVT = 8.0085662595372944372e-17;

	// High/low words workspace:
	var WORDS = [ 0|0, 0|0 ];

	// Log workspace:
	var LOG_WORKSPACE = [ 0.0, 0.0 ];


	// MAIN //

	/**
	* Evaluates the exponential function.
	*
	* ## Method
	*
	* 1.  Let \\(x = 2^n (1+f)\\).
	*
	* 2.  Compute \\(\operatorname{log2}(x)\\) as
	*
	*     ```tex
	*     \operatorname{log2}(x) = w_1 + w_2
	*     ```
	*
	*     where \\(w_1\\) has \\(53 - 24 = 29\\) bit trailing zeros.
	*
	* 3.  Compute
	*
	*     ```tex
	*     y \cdot \operatorname{log2}(x) = n + y^\prime
	*     ```
	*
	*     by simulating multi-precision arithmetic, where \\(|y^\prime| \leq 0.5\\).
	*
	* 4.  Return
	*
	*     ```tex
	*     x^y = 2^n e^{y^\prime \cdot \mathrm{log2}}
	*     ```
	*
	* ## Special Cases
	*
	* ```tex
	* \begin{align*}
	* x^{\mathrm{NaN}} &= \mathrm{NaN} & \\
	* (\mathrm{NaN})^y &= \mathrm{NaN} & \\
	* 1^y &= 1 & \\
	* x^0 &= 1 & \\
	* x^1 &= x & \\
	* (\pm 0)^\infty &= +0 & \\
	* (\pm 0)^{-\infty} &= +\infty & \\
	* (+0)^y &= +0 & \mathrm{if}\ y > 0 \\
	* (+0)^y &= +\infty & \mathrm{if}\ y < 0 \\
	* (-0)^y &= -\infty & \mathrm{if}\ y\ \mathrm{is\ an\ odd\ integer\ and}\ y < 0 \\
	* (-0)^y &= +\infty & \mathrm{if}\ y\ \mathrm{is\ not\ an\ odd\ integer\ and}\ y < 0 \\
	* (-0)^y &= -0 & \mathrm{if}\ y\ \mathrm{is\ an\ odd\ integer\ and}\ y > 0 \\
	* (-0)^y &= +0 & \mathrm{if}\ y\ \mathrm{is\ not\ an\ odd\ integer\ and}\ y > 0 \\
	* (-1)^{\pm\infty} &= \mathrm{NaN} & \\
	* x^{\infty} &= +\infty & |x| > 1 \\
	* x^{\infty} &= +0 & |x| < 1 \\
	* x^{-\infty} &= +0 & |x| > 1 \\
	* x^{-\infty} &= +\infty & |x| < 1 \\
	* (-\infty)^y &= (-0)^y & \\
	* \infty^y &= +0 & y < 0 \\
	* \infty^y &= +\infty & y > 0 \\
	* x^y &= \mathrm{NaN} & \mathrm{if}\ y\ \mathrm{is\ not\ a\ finite\ integer\ and}\ x < 0
	* \end{align*}
	* ```
	*
	* ## Notes
	*
	* -   \\(\operatorname{pow}(x,y)\\) returns \\(x^y\\) nearly rounded. In particular, \\(\operatorname{pow}(<\mathrm{integer}>,<\mathrm{integer}>)\\) **always** returns the correct integer, provided the value is representable.
	* -   The hexadecimal values shown in the source code are the intended values for used constants. Decimal values may be used, provided the compiler will accurately convert decimal to binary in order to produce the hexadecimal values.
	*
	* @param {number} x - base
	* @param {number} y - exponent
	* @returns {number} function value
	*
	* @example
	* var v = pow( 2.0, 3.0 );
	* // returns 8.0
	*
	* @example
	* var v = pow( 4.0, 0.5 );
	* // returns 2.0
	*
	* @example
	* var v = pow( 100.0, 0.0 );
	* // returns 1.0
	*
	* @example
	* var v = pow( 3.141592653589793, 5.0 );
	* // returns ~306.0197
	*
	* @example
	* var v = pow( 3.141592653589793, -0.2 );
	* // returns ~0.7954
	*
	* @example
	* var v = pow( NaN, 3.0 );
	* // returns NaN
	*
	* @example
	* var v = pow( 5.0, NaN );
	* // returns NaN
	*
	* @example
	* var v = pow( NaN, NaN );
	* // returns NaN
	*/
	function pow( x, y ) {
		var ahx; // absolute value high word `x`
		var ahy; // absolute value high word `y`
		var ax;  // absolute value `x`
		var hx;  // high word `x`
		var lx;  // low word `x`
		var hy;  // high word `y`
		var ly;  // low word `y`
		var sx;  // sign `x`
		var sy;  // sign `y`
		var y1;
		var hp;
		var lp;
		var t;
		var z;   // y prime
		var j;
		var i;
		if ( isnan( x ) || isnan( y ) ) {
			return NaN;
		}
		// Split `y` into high and low words:
		toWords.assign( y, WORDS, 1, 0 );
		hy = WORDS[ 0 ];
		ly = WORDS[ 1 ];

		// Special cases `y`...
		if ( ly === 0 ) {
			if ( y === 0.0 ) {
				return 1.0;
			}
			if ( y === 1.0 ) {
				return x;
			}
			if ( y === -1.0 ) {
				return 1.0 / x;
			}
			if ( y === 0.5 ) {
				return sqrt( x );
			}
			if ( y === -0.5 ) {
				return 1.0 / sqrt( x );
			}
			if ( y === 2.0 ) {
				return x * x;
			}
			if ( y === 3.0 ) {
				return x * x * x;
			}
			if ( y === 4.0 ) {
				x *= x;
				return x * x;
			}
			if ( isInfinite( y ) ) {
				return yIsInfinite( x, y );
			}
		}
		// Split `x` into high and low words:
		toWords.assign( x, WORDS, 1, 0 );
		hx = WORDS[ 0 ];
		lx = WORDS[ 1 ];

		// Special cases `x`...
		if ( lx === 0 ) {
			if ( hx === 0 ) {
				return xIsZero( x, y );
			}
			if ( x === 1.0 ) {
				return 1.0;
			}
			if (
				x === -1.0 &&
				isOdd( y )
			) {
				return -1.0;
			}
			if ( isInfinite( x ) ) {
				if ( x === NINF ) {
					// `pow( 1/x, -y )`
					return pow( -0.0, -y );
				}
				if ( y < 0.0 ) {
					return 0.0;
				}
				return PINF;
			}
		}
		if (
			x < 0.0 &&
			isInteger( y ) === false
		) {
			// Signal NaN...
			return (x-x)/(x-x);
		}
		ax = abs( x );

		// Remove the sign bits (i.e., get absolute values):
		ahx = (hx & ABS_MASK)|0; // asm type annotation
		ahy = (hy & ABS_MASK)|0; // asm type annotation

		// Extract the sign bits:
		sx = (hx >>> HIGH_NUM_NONSIGN_BITS)|0; // asm type annotation
		sy = (hy >>> HIGH_NUM_NONSIGN_BITS)|0; // asm type annotation

		// Determine the sign of the result...
		if ( sx && isOdd( y ) ) {
			sx = -1.0;
		} else {
			sx = 1.0;
		}
		// Case 1: `|y|` is huge...

		// |y| > 2^31
		if ( ahy > HIGH_BIASED_EXP_31 ) {
			// `|y| > 2^64`, then must over- or underflow...
			if ( ahy > HIGH_BIASED_EXP_64 ) {
				return yIsHuge( x, y );
			}
			// Over- or underflow if `x` is not close to unity...

			if ( ahx < HIGH_MAX_NEAR_UNITY ) {
				// y < 0
				if ( sy === 1 ) {
					// Signal overflow...
					return sx * HUGE * HUGE;
				}
				// Signal underflow...
				return sx * TINY * TINY;
			}
			if ( ahx > HIGH_BIASED_EXP_0 ) {
				// y > 0
				if ( sy === 0 ) {
					// Signal overflow...
					return sx * HUGE * HUGE;
				}
				// Signal underflow...
				return sx * TINY * TINY;
			}
			// At this point, `|1-x|` is tiny (`<= 2^-20`). Suffice to compute `log(x)` by `x - x^2/2 + x^3/3 - x^4/4`.
			t = logx( LOG_WORKSPACE, ax );
		}
		// Case 2: `|y|` is not huge...
		else {
			t = log2ax( LOG_WORKSPACE, ax, ahx );
		}
		// Split `y` into `y1 + y2` and compute `(y1+y2) * (t1+t2)`...
		y1 = setLowWord( y, 0 );
		lp = ( (y-y1)*t[0] ) + ( y*t[1] );
		hp = y1 * t[0];
		z = lp + hp;

		// Note: *can* be more performant to use `getHighWord` and `getLowWord` directly, but using `toWords` looks cleaner.
		toWords.assign( z, WORDS, 1, 0 );
		j = uint32ToInt32( WORDS[0] );
		i = uint32ToInt32( WORDS[1] );

		// z >= 1024
		if ( j >= HIGH_BIASED_EXP_10 ) {
			// z > 1024
			if ( ((j-HIGH_BIASED_EXP_10)|i) !== 0 ) {
				// Signal overflow...
				return sx * HUGE * HUGE;
			}
			if ( (lp+OVT) > (z-hp) ) {
				// Signal overflow...
				return sx * HUGE * HUGE;
			}
		}
		// z <= -1075
		else if ( (j&ABS_MASK) >= HIGH_1075 ) {
			// z < -1075
			if ( ((j-HIGH_NEG_1075)|i) !== 0 ) {
				// Signal underflow...
				return sx * TINY * TINY;
			}
			if ( lp <= (z-hp) ) {
				// Signal underflow...
				return sx * TINY * TINY;
			}
		}
		// Compute `2^(hp+lp)`...
		z = pow2( j, hp, lp );

		return sx * z;
	}


	// EXPORTS //

	main$7 = pow;
	return main$7;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$8;
var hasRequiredLib$8;

function requireLib$8 () {
	if (hasRequiredLib$8) return lib$8;
	hasRequiredLib$8 = 1;

	/**
	* Evaluate the exponential function.
	*
	* @module @stdlib/math-base-special-pow
	*
	* @example
	* var pow = require( '@stdlib/math-base-special-pow' );
	*
	* var v = pow( 2.0, 3.0 );
	* // returns 8.0
	*
	* v = pow( 4.0, 0.5 );
	* // returns 2.0
	*
	* v = pow( 100.0, 0.0 );
	* // returns 1.0
	*
	* v = pow( 3.141592653589793, 5.0 );
	* // returns ~306.0197
	*
	* v = pow( 3.141592653589793, -0.2 );
	* // returns ~0.7954
	*
	* v = pow( NaN, 3.0 );
	* // returns NaN
	*
	* v = pow( 5.0, NaN );
	* // returns NaN
	*
	* v = pow( NaN, NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$7();


	// EXPORTS //

	lib$8 = main;
	return lib$8;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$6;
var hasRequiredMain$6;

function requireMain$6 () {
	if (hasRequiredMain$6) return main$6;
	hasRequiredMain$6 = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var exp = requireLib$L();
	var pow = requireLib$8();


	// MAIN //

	/**
	* Evaluates the moment-generating function (MGF) for a normal distribution with mean `mu` and standard deviation `sigma` at a value `t`.
	*
	* @param {number} t - input value
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {number} evaluated MGF
	*
	* @example
	* var y = mgf( 2.0, 0.0, 1.0 );
	* // returns ~7.389
	*
	* @example
	* var y = mgf( 0.0, 0.0, 1.0 );
	* // returns 1.0
	*
	* @example
	* var y = mgf( -1.0, 4.0, 2.0 );
	* // returns ~0.1353
	*
	* @example
	* var y = mgf( NaN, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = mgf( 0.0, NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = mgf( 0.0, 0.0, NaN );
	* // returns NaN
	*
	* @example
	* var y = mgf( 2.0, 0.0, 0.0 );
	* // returns NaN
	*/
	function mgf( t, mu, sigma ) {
		if (
			isnan( t ) ||
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return NaN;
		}
		return exp( (mu * t) + (0.5 * pow( sigma * t, 2.0 )) );
	}


	// EXPORTS //

	main$6 = mgf;
	return main$6;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$4;
var hasRequiredFactory$4;

function requireFactory$4 () {
	if (hasRequiredFactory$4) return factory_1$4;
	hasRequiredFactory$4 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var isnan = requireLib$1s();
	var exp = requireLib$L();
	var pow = requireLib$8();


	// MAIN //

	/**
	* Returns a function for evaluating the moment-generating function (MGF) of a normal distribution with mean `mu` and standard deviation `sigma`.
	*
	* @param {number} mu - mean
	* @param {PositiveNumber} sigma - standard deviation
	* @returns {Function} MGF
	*
	* @example
	* var mgf = factory( 4.0, 2.0 );
	*
	* var y = mgf( 1.0 );
	* // returns ~403.429
	*
	* y = mgf( 0.5 );
	* // returns ~12.182
	*/
	function factory( mu, sigma ) {
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma <= 0.0
		) {
			return constantFunction( NaN );
		}
		return mgf;

		/**
		* Evaluates the moment-generating function (MGF) for a normal distribution.
		*
		* @private
		* @param {number} t - input value
		* @returns {number} evaluated MGF
		*
		* @example
		* var y = mgf( 0.5 );
		* // returns <number>
		*/
		function mgf( t ) {
			if ( isnan( t ) ) {
				return NaN;
			}
			return exp( (mu * t) + (0.5 * pow( sigma * t, 2.0 )) );
		}
	}


	// EXPORTS //

	factory_1$4 = factory;
	return factory_1$4;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$7;
var hasRequiredLib$7;

function requireLib$7 () {
	if (hasRequiredLib$7) return lib$7;
	hasRequiredLib$7 = 1;

	/**
	* Evaluate the moment-generating function (MGF) for a normal distribution.
	*
	* @module @stdlib/stats-base-dists-normal-mgf
	*
	* @example
	* var mgf = require( '@stdlib/stats-base-dists-normal-mgf' );
	*
	* var y = mgf( 2.0, 0.0, 1.0 );
	* // returns ~7.389
	*
	* y = mgf( 0.0, 0.0, 1.0 );
	* // returns 1.0
	*
	* y = mgf( -1.0, 4.0, 2.0 );
	* // returns ~0.1353
	*
	* var mymgf = mgf.factory( 4.0, 2.0 );
	*
	* y = mymgf( 1.0 );
	* // returns ~403.429
	*
	* y = mymgf( 0.5 );
	* // returns ~12.182
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$6();
	var factory = requireFactory$4();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$7 = main;
	return lib$7;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$5;
var hasRequiredMain$5;

function requireMain$5 () {
	if (hasRequiredMain$5) return main$5;
	hasRequiredMain$5 = 1;

	// MODULES //

	var exp = requireLib$L();
	var pow = requireLib$8();
	var sqrt = requireLib$I();
	var TWO_PI = requireLib$y();
	var PINF = requireLib$1m();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the probability density function (PDF) for a normal distribution with mean `mu` and standard deviation `sigma` at a value `x`.
	*
	* @param {number} x - input value
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {number} evaluated probability density function
	*
	* @example
	* var y = pdf( 2.0, 0.0, 1.0 );
	* // returns ~0.054
	*
	* @example
	* var y = pdf( -1.0, 4.0, 2.0 );
	* // returns ~0.009
	*
	* @example
	* var y = pdf( NaN, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = pdf( 0.0, NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = pdf( 0.0, 0.0, NaN );
	* // returns NaN
	*
	* @example
	* // Negative standard deviation:
	* var y = pdf( 2.0, 0.0, -1.0 );
	* // returns NaN
	*
	* @example
	* var y = pdf( 2.0, 8.0, 0.0 );
	* // returns 0.0
	*
	* @example
	* var y = pdf( 8.0, 8.0, 0.0 );
	* // returns Infinity
	*/
	function pdf( x, mu, sigma ) {
		var s2;
		var A;
		var B;
		if (
			isnan( x ) ||
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return NaN;
		}
		if ( sigma === 0.0 ) {
			return ( x === mu ) ? PINF : 0.0;
		}
		s2 = pow( sigma, 2.0 );
		A = 1.0 / sqrt( s2*TWO_PI );
		B = -1.0 / ( 2.0*s2 );
		return A * exp( B * pow(x-mu, 2.0) );
	}


	// EXPORTS //

	main$5 = pdf;
	return main$5;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$4;
var hasRequiredMain$4;

function requireMain$4 () {
	if (hasRequiredMain$4) return main$4;
	hasRequiredMain$4 = 1;

	// MODULES //

	var PINF = requireLib$1m();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the probability density function (PDF) for a degenerate distribution centered at `mu`.
	*
	* @param {number} x - input value
	* @param {number} mu - constant value of the distribution
	* @returns {number} evaluated probability density function
	*
	* @example
	* var y = pdf( 2.0, 3.0 );
	* // returns 0.0
	*
	* @example
	* var y = pdf( 3.0, 3.0 );
	* // returns Infinity
	*
	* @example
	* var y = pdf( NaN, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = pdf( 0.0, NaN );
	* // returns NaN
	*/
	function pdf( x, mu ) {
		if ( isnan( x ) || isnan( mu ) ) {
			return NaN;
		}
		return ( x === mu ) ? PINF : 0.0;
	}


	// EXPORTS //

	main$4 = pdf;
	return main$4;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$3;
var hasRequiredFactory$3;

function requireFactory$3 () {
	if (hasRequiredFactory$3) return factory_1$3;
	hasRequiredFactory$3 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var PINF = requireLib$1m();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns a function for evaluating the probability density function (PDF) of a degenerate distribution centered at a provided mean value.
	*
	* @param {number} mu - value at which to center the distribution
	* @returns {Function} function to evaluate the probability density function
	*
	* @example
	* var pdf = factory( 5.0 );
	*
	* var y = pdf( 0.0 );
	* // returns 0.0
	*
	* y = pdf( 5.0 );
	* // returns Infinity
	*/
	function factory( mu ) {
		if ( isnan( mu ) ) {
			return constantFunction( NaN );
		}
		return pdf;

		/**
		* Evaluates the probability density function (PDF) of a degenerate distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {number} evaluated probability density function
		*
		* @example
		* var y = pdf( 10.0 );
		* // returns <number>
		*/
		function pdf( x ) {
			if ( isnan( x ) ) {
				return NaN;
			}
			return ( x === mu ) ? PINF : 0.0;
		}
	}


	// EXPORTS //

	factory_1$3 = factory;
	return factory_1$3;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$6;
var hasRequiredLib$6;

function requireLib$6 () {
	if (hasRequiredLib$6) return lib$6;
	hasRequiredLib$6 = 1;

	/**
	* Degenerate distribution probability density function (PDF).
	*
	* @module @stdlib/stats-base-dists-degenerate-pdf
	*
	* @example
	* var pdf = require( '@stdlib/stats-base-dists-degenerate-pdf' );
	*
	* var y = pdf( 2.0, 0.0 );
	* // returns 0.0
	*
	* @example
	* var factory = require( '@stdlib/stats-base-dists-degenerate-pdf' ).factory;
	*
	* var pdf = factory( 10.0 );
	*
	* var y = pdf( 10.0 );
	* // returns Infinity
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$4();
	var factory = requireFactory$3();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$6 = main;
	return lib$6;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$2;
var hasRequiredFactory$2;

function requireFactory$2 () {
	if (hasRequiredFactory$2) return factory_1$2;
	hasRequiredFactory$2 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var degenerate = requireLib$6().factory;
	var isnan = requireLib$1s();
	var sqrt = requireLib$I();
	var exp = requireLib$L();
	var pow = requireLib$8();
	var TWO_PI = requireLib$y();


	// MAIN //

	/**
	* Returns a function for evaluating the probability density function (PDF) for a normal distribution.
	*
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {Function} function to evaluate the probability density function
	*
	* @example
	* var pdf = factory( 10.0, 2.0 );
	* var y = pdf( 10.0 );
	* // returns ~0.199
	*
	* y = pdf( 5.0 );
	* // returns ~0.009
	*/
	function factory( mu, sigma ) {
		var s2;
		var A;
		var B;
		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			sigma < 0.0
		) {
			return constantFunction( NaN );
		}
		if ( sigma === 0.0 ) {
			return degenerate( mu );
		}
		s2 = pow( sigma, 2.0 );
		A = 1.0 / sqrt( s2*TWO_PI );
		B = -1.0 / ( 2.0*s2 );
		return pdf;

		/**
		* Evaluates the probability density function (PDF) for a normal distribution.
		*
		* @private
		* @param {number} x - input value
		* @returns {number} evaluated probability density function
		*
		* @example
		* var y = pdf( -3.14 );
		* // returns <number>
		*/
		function pdf( x ) {
			if ( isnan( x ) ) {
				return NaN;
			}
			return A * exp( B * pow(x-mu, 2.0) );
		}
	}


	// EXPORTS //

	factory_1$2 = factory;
	return factory_1$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$5;
var hasRequiredLib$5;

function requireLib$5 () {
	if (hasRequiredLib$5) return lib$5;
	hasRequiredLib$5 = 1;

	/**
	* Normal distribution probability density function (PDF).
	*
	* @module @stdlib/stats-base-dists-normal-pdf
	*
	* @example
	* var pdf = require( '@stdlib/stats-base-dists-normal-pdf' );
	*
	* var y = pdf( 2.0, 0.0, 1.0 );
	* // returns ~0.054
	*
	* var myPDF = pdf.factory( 10.0, 2.0 );
	* y = myPDF( 10.0 );
	* // returns ~0.199
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$5();
	var factory = requireFactory$2();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$5 = main;
	return lib$5;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var rational_p1q1;
var hasRequiredRational_p1q1;

function requireRational_p1q1 () {
	if (hasRequiredRational_p1q1) return rational_p1q1;
	hasRequiredRational_p1q1 = 1;

	// MAIN //

	/**
	* Evaluates a rational function, i.e., the ratio of two polynomials described by the coefficients stored in \\(P\\) and \\(Q\\).
	*
	* ## Notes
	*
	* -   Coefficients should be sorted in ascending degree.
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the rational function
	* @returns {number} evaluated rational function
	*/
	function evalrational( x ) {
		var ax;
		var s1;
		var s2;
		if ( x === 0.0 ) {
			return -0.0005087819496582806;
		}
		if ( x < 0.0 ) {
			ax = -x;
		} else {
			ax = x;
		}
		if ( ax <= 1.0 ) {
			s1 = -0.0005087819496582806 + (x * (-0.008368748197417368 + (x * (0.03348066254097446 + (x * (-0.012692614766297404 + (x * (-0.03656379714117627 + (x * (0.02198786811111689 + (x * (0.008226878746769157 + (x * (-0.005387729650712429 + (x * (0.0 + (x * 0.0))))))))))))))))); // eslint-disable-line max-len
			s2 = 1.0 + (x * (-0.9700050433032906 + (x * (-1.5657455823417585 + (x * (1.5622155839842302 + (x * (0.662328840472003 + (x * (-0.7122890234154284 + (x * (-0.05273963823400997 + (x * (0.07952836873415717 + (x * (-0.0023339375937419 + (x * 0.0008862163904564247))))))))))))))))); // eslint-disable-line max-len
		} else {
			x = 1.0 / x;
			s1 = 0.0 + (x * (0.0 + (x * (-0.005387729650712429 + (x * (0.008226878746769157 + (x * (0.02198786811111689 + (x * (-0.03656379714117627 + (x * (-0.012692614766297404 + (x * (0.03348066254097446 + (x * (-0.008368748197417368 + (x * -0.0005087819496582806))))))))))))))))); // eslint-disable-line max-len
			s2 = 0.0008862163904564247 + (x * (-0.0023339375937419 + (x * (0.07952836873415717 + (x * (-0.05273963823400997 + (x * (-0.7122890234154284 + (x * (0.662328840472003 + (x * (1.5622155839842302 + (x * (-1.5657455823417585 + (x * (-0.9700050433032906 + (x * 1.0))))))))))))))))); // eslint-disable-line max-len
		}
		return s1 / s2;
	}


	// EXPORTS //

	rational_p1q1 = evalrational;
	return rational_p1q1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var rational_p2q2;
var hasRequiredRational_p2q2;

function requireRational_p2q2 () {
	if (hasRequiredRational_p2q2) return rational_p2q2;
	hasRequiredRational_p2q2 = 1;

	// MAIN //

	/**
	* Evaluates a rational function, i.e., the ratio of two polynomials described by the coefficients stored in \\(P\\) and \\(Q\\).
	*
	* ## Notes
	*
	* -   Coefficients should be sorted in ascending degree.
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the rational function
	* @returns {number} evaluated rational function
	*/
	function evalrational( x ) {
		var ax;
		var s1;
		var s2;
		if ( x === 0.0 ) {
			return -0.20243350835593876;
		}
		if ( x < 0.0 ) {
			ax = -x;
		} else {
			ax = x;
		}
		if ( ax <= 1.0 ) {
			s1 = -0.20243350835593876 + (x * (0.10526468069939171 + (x * (8.3705032834312 + (x * (17.644729840837403 + (x * (-18.851064805871424 + (x * (-44.6382324441787 + (x * (17.445385985570866 + (x * (21.12946554483405 + (x * -3.6719225470772936))))))))))))))); // eslint-disable-line max-len
			s2 = 1.0 + (x * (6.242641248542475 + (x * (3.971343795334387 + (x * (-28.66081804998 + (x * (-20.14326346804852 + (x * (48.560921310873994 + (x * (10.826866735546016 + (x * (-22.643693341313973 + (x * 1.7211476576120028))))))))))))))); // eslint-disable-line max-len
		} else {
			x = 1.0 / x;
			s1 = -3.6719225470772936 + (x * (21.12946554483405 + (x * (17.445385985570866 + (x * (-44.6382324441787 + (x * (-18.851064805871424 + (x * (17.644729840837403 + (x * (8.3705032834312 + (x * (0.10526468069939171 + (x * -0.20243350835593876))))))))))))))); // eslint-disable-line max-len
			s2 = 1.7211476576120028 + (x * (-22.643693341313973 + (x * (10.826866735546016 + (x * (48.560921310873994 + (x * (-20.14326346804852 + (x * (-28.66081804998 + (x * (3.971343795334387 + (x * (6.242641248542475 + (x * 1.0))))))))))))))); // eslint-disable-line max-len
		}
		return s1 / s2;
	}


	// EXPORTS //

	rational_p2q2 = evalrational;
	return rational_p2q2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var rational_p3q3;
var hasRequiredRational_p3q3;

function requireRational_p3q3 () {
	if (hasRequiredRational_p3q3) return rational_p3q3;
	hasRequiredRational_p3q3 = 1;

	// MAIN //

	/**
	* Evaluates a rational function, i.e., the ratio of two polynomials described by the coefficients stored in \\(P\\) and \\(Q\\).
	*
	* ## Notes
	*
	* -   Coefficients should be sorted in ascending degree.
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the rational function
	* @returns {number} evaluated rational function
	*/
	function evalrational( x ) {
		var ax;
		var s1;
		var s2;
		if ( x === 0.0 ) {
			return -0.1311027816799519;
		}
		if ( x < 0.0 ) {
			ax = -x;
		} else {
			ax = x;
		}
		if ( ax <= 1.0 ) {
			s1 = -0.1311027816799519 + (x * (-0.16379404719331705 + (x * (0.11703015634199525 + (x * (0.38707973897260434 + (x * (0.3377855389120359 + (x * (0.14286953440815717 + (x * (0.029015791000532906 + (x * (0.0021455899538880526 + (x * (-6.794655751811263e-7 + (x * (2.8522533178221704e-8 + (x * -6.81149956853777e-10))))))))))))))))))); // eslint-disable-line max-len
			s2 = 1.0 + (x * (3.4662540724256723 + (x * (5.381683457070069 + (x * (4.778465929458438 + (x * (2.5930192162362027 + (x * (0.848854343457902 + (x * (0.15226433829533179 + (x * (0.011059242293464892 + (x * (0.0 + (x * (0.0 + (x * 0.0))))))))))))))))))); // eslint-disable-line max-len
		} else {
			x = 1.0 / x;
			s1 = -6.81149956853777e-10 + (x * (2.8522533178221704e-8 + (x * (-6.794655751811263e-7 + (x * (0.0021455899538880526 + (x * (0.029015791000532906 + (x * (0.14286953440815717 + (x * (0.3377855389120359 + (x * (0.38707973897260434 + (x * (0.11703015634199525 + (x * (-0.16379404719331705 + (x * -0.1311027816799519))))))))))))))))))); // eslint-disable-line max-len
			s2 = 0.0 + (x * (0.0 + (x * (0.0 + (x * (0.011059242293464892 + (x * (0.15226433829533179 + (x * (0.848854343457902 + (x * (2.5930192162362027 + (x * (4.778465929458438 + (x * (5.381683457070069 + (x * (3.4662540724256723 + (x * 1.0))))))))))))))))))); // eslint-disable-line max-len
		}
		return s1 / s2;
	}


	// EXPORTS //

	rational_p3q3 = evalrational;
	return rational_p3q3;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var rational_p4q4;
var hasRequiredRational_p4q4;

function requireRational_p4q4 () {
	if (hasRequiredRational_p4q4) return rational_p4q4;
	hasRequiredRational_p4q4 = 1;

	// MAIN //

	/**
	* Evaluates a rational function, i.e., the ratio of two polynomials described by the coefficients stored in \\(P\\) and \\(Q\\).
	*
	* ## Notes
	*
	* -   Coefficients should be sorted in ascending degree.
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the rational function
	* @returns {number} evaluated rational function
	*/
	function evalrational( x ) {
		var ax;
		var s1;
		var s2;
		if ( x === 0.0 ) {
			return -0.0350353787183178;
		}
		if ( x < 0.0 ) {
			ax = -x;
		} else {
			ax = x;
		}
		if ( ax <= 1.0 ) {
			s1 = -0.0350353787183178 + (x * (-0.0022242652921344794 + (x * (0.018557330651423107 + (x * (0.009508047013259196 + (x * (0.0018712349281955923 + (x * (0.00015754461742496055 + (x * (0.00000460469890584318 + (x * (-2.304047769118826e-10 + (x * 2.6633922742578204e-12))))))))))))))); // eslint-disable-line max-len
			s2 = 1.0 + (x * (1.3653349817554064 + (x * (0.7620591645536234 + (x * (0.22009110576413124 + (x * (0.03415891436709477 + (x * (0.00263861676657016 + (x * (0.00007646752923027944 + (x * (0.0 + (x * 0.0))))))))))))))); // eslint-disable-line max-len
		} else {
			x = 1.0 / x;
			s1 = 2.6633922742578204e-12 + (x * (-2.304047769118826e-10 + (x * (0.00000460469890584318 + (x * (0.00015754461742496055 + (x * (0.0018712349281955923 + (x * (0.009508047013259196 + (x * (0.018557330651423107 + (x * (-0.0022242652921344794 + (x * -0.0350353787183178))))))))))))))); // eslint-disable-line max-len
			s2 = 0.0 + (x * (0.0 + (x * (0.00007646752923027944 + (x * (0.00263861676657016 + (x * (0.03415891436709477 + (x * (0.22009110576413124 + (x * (0.7620591645536234 + (x * (1.3653349817554064 + (x * 1.0))))))))))))))); // eslint-disable-line max-len
		}
		return s1 / s2;
	}


	// EXPORTS //

	rational_p4q4 = evalrational;
	return rational_p4q4;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var rational_p5q5;
var hasRequiredRational_p5q5;

function requireRational_p5q5 () {
	if (hasRequiredRational_p5q5) return rational_p5q5;
	hasRequiredRational_p5q5 = 1;

	// MAIN //

	/**
	* Evaluates a rational function, i.e., the ratio of two polynomials described by the coefficients stored in \\(P\\) and \\(Q\\).
	*
	* ## Notes
	*
	* -   Coefficients should be sorted in ascending degree.
	* -   The implementation uses [Horner's rule][horners-method] for efficient computation.
	*
	* [horners-method]: https://en.wikipedia.org/wiki/Horner%27s_method
	*
	* @private
	* @param {number} x - value at which to evaluate the rational function
	* @returns {number} evaluated rational function
	*/
	function evalrational( x ) {
		var ax;
		var s1;
		var s2;
		if ( x === 0.0 ) {
			return -0.016743100507663373;
		}
		if ( x < 0.0 ) {
			ax = -x;
		} else {
			ax = x;
		}
		if ( ax <= 1.0 ) {
			s1 = -0.016743100507663373 + (x * (-0.0011295143874558028 + (x * (0.001056288621524929 + (x * (0.00020938631748758808 + (x * (0.000014962478375834237 + (x * (4.4969678992770644e-7 + (x * (4.625961635228786e-9 + (x * (-2.811287356288318e-14 + (x * 9.905570997331033e-17))))))))))))))); // eslint-disable-line max-len
			s2 = 1.0 + (x * (0.5914293448864175 + (x * (0.1381518657490833 + (x * (0.016074608709367652 + (x * (0.0009640118070051656 + (x * (0.000027533547476472603 + (x * (2.82243172016108e-7 + (x * (0.0 + (x * 0.0))))))))))))))); // eslint-disable-line max-len
		} else {
			x = 1.0 / x;
			s1 = 9.905570997331033e-17 + (x * (-2.811287356288318e-14 + (x * (4.625961635228786e-9 + (x * (4.4969678992770644e-7 + (x * (0.000014962478375834237 + (x * (0.00020938631748758808 + (x * (0.001056288621524929 + (x * (-0.0011295143874558028 + (x * -0.016743100507663373))))))))))))))); // eslint-disable-line max-len
			s2 = 0.0 + (x * (0.0 + (x * (2.82243172016108e-7 + (x * (0.000027533547476472603 + (x * (0.0009640118070051656 + (x * (0.016074608709367652 + (x * (0.1381518657490833 + (x * (0.5914293448864175 + (x * 1.0))))))))))))))); // eslint-disable-line max-len
		}
		return s1 / s2;
	}


	// EXPORTS //

	rational_p5q5 = evalrational;
	return rational_p5q5;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*
* ## Notice
*
* The original C++ code and copyright notice are from the [Boost library]{@link http://www.boost.org/doc/libs/1_48_0/boost/math/special_functions/detail/erf_inv.hpp}. This implementation follows the original, but has been modified for JavaScript.
*
* ```text
* (C) Copyright John Maddock 2006.
*
* Use, modification and distribution are subject to the
* Boost Software License, Version 1.0. (See accompanying file
* LICENSE or copy at http://www.boost.org/LICENSE_1_0.txt)
* ```
*/

var main$3;
var hasRequiredMain$3;

function requireMain$3 () {
	if (hasRequiredMain$3) return main$3;
	hasRequiredMain$3 = 1;

	// MODULES //

	var isnan = requireLib$1s();
	var sqrt = requireLib$I();
	var ln = requireLib$z();
	var PINF = requireLib$1m();
	var NINF = requireLib$1n();
	var rationalFcnR1 = requireRational_p1q1();
	var rationalFcnR2 = requireRational_p2q2();
	var rationalFcnR3 = requireRational_p3q3();
	var rationalFcnR4 = requireRational_p4q4();
	var rationalFcnR5 = requireRational_p5q5();


	// VARIABLES //

	var Y1 = 8.91314744949340820313e-2;
	var Y2 = 2.249481201171875;
	var Y3 = 8.07220458984375e-1;
	var Y4 = 9.3995571136474609375e-1;
	var Y5 = 9.8362827301025390625e-1;


	// MAIN //

	/**
	* Evaluates the inverse error function.
	*
	* ## Method
	*
	* 1.  For \\(|x| \leq 0.5\\), we evaluate the inverse error function using the rational approximation
	*
	*     ```tex
	*     \operatorname{erf^{-1}}(x) = x(x+10)(\mathrm{Y} + \operatorname{R}(x))
	*     ```
	*
	*     where \\(Y\\) is a constant and \\(\operatorname{R}(x)\\) is optimized for a low absolute error compared to \\(|Y|\\).
	*
	*     <!-- <note> -->
	*
	*     Max error \\(2.001849\mbox{e-}18\\). Maximum deviation found (error term at infinite precision) \\(8.030\mbox{e-}21\\).
	*
	*     <!-- </note> -->
	*
	* 2.  For \\(0.5 > 1-|x| \geq 0\\), we evaluate the inverse error function using the rational approximation
	*
	*     ```tex
	*     \operatorname{erf^{-1}} = \frac{\sqrt{-2 \cdot \ln(1-x)}}{\mathrm{Y} + \operatorname{R}(1-x)}
	*     ```
	*
	*     where \\(Y\\) is a constant, and \\(\operatorname{R}(q)\\) is optimized for a low absolute error compared to \\(Y\\).
	*
	*     <!-- <note> -->
	*
	*     Max error \\(7.403372\mbox{e-}17\\). Maximum deviation found (error term at infinite precision) \\(4.811\mbox{e-}20\\).
	*
	*     <!-- </note> -->
	*
	* 3.  For \\(1-|x| < 0.25\\), we have a series of rational approximations all of the general form
	*
	*     ```tex
	*     p = \sqrt{-\ln(1-x)}
	*     ```
	*
	*     Accordingly, the result is given by
	*
	*     ```tex
	*     \operatorname{erf^{-1}}(x) = p(\mathrm{Y} + \operatorname{R}(p-B))
	*     ```
	*
	*     where \\(Y\\) is a constant, \\(B\\) is the lowest value of \\(p\\) for which the approximation is valid, and \\(\operatorname{R}(x-B)\\) is optimized for a low absolute error compared to \\(Y\\).
	*
	*     <!-- <note> -->
	*
	*     Almost all code will only go through the first or maybe second approximation.  After that we are dealing with very small input values.
	*
	*     -   If \\(p < 3\\), max error \\(1.089051\mbox{e-}20\\).
	*     -   If \\(p < 6\\), max error \\(8.389174\mbox{e-}21\\).
	*     -   If \\(p < 18\\), max error \\(1.481312\mbox{e-}19\\).
	*     -   If \\(p < 44\\), max error \\(5.697761\mbox{e-}20\\).
	*     -   If \\(p \geq 44\\), max error \\(1.279746\mbox{e-}20\\).
	*
	*     <!-- </note> -->
	*
	*     <!-- <note> -->
	*
	*     The Boost library can accommodate \\(80\\) and \\(128\\) bit long doubles. JavaScript only supports a \\(64\\) bit double (IEEE 754). Accordingly, the smallest \\(p\\) (in JavaScript at the time of this writing) is \\(\sqrt{-\ln(\sim5\mbox{e-}324)} = 27.284429111150214\\).
	*
	*     <!-- </note> -->
	*
	* @param {number} x - input value
	* @returns {number} function value
	*
	* @example
	* var y = erfinv( 0.5 );
	* // returns ~0.4769
	*
	* @example
	* var y = erfinv( 0.8 );
	* // returns ~0.9062
	*
	* @example
	* var y = erfinv( 0.0 );
	* // returns 0.0
	*
	* @example
	* var y = erfinv( -0.0 );
	* // returns -0.0
	*
	* @example
	* var y = erfinv( -1.0 );
	* // returns -Infinity
	*
	* @example
	* var y = erfinv( 1.0 );
	* // returns Infinity
	*
	* @example
	* var y = erfinv( NaN );
	* // returns NaN
	*/
	function erfinv( x ) {
		var sign;
		var ax;
		var qs;
		var q;
		var g;
		var r;

		// Special case: NaN
		if ( isnan( x ) ) {
			return NaN;
		}
		// Special case: 1
		if ( x === 1.0 ) {
			return PINF;
		}
		// Special case: -1
		if ( x === -1.0 ) {
			return NINF;
		}
		// Special case: +-0
		if ( x === 0.0 ) {
			return x;
		}
		// Special case: |x| > 1 (range error)
		if ( x > 1.0 || x < -1.0 ) {
			return NaN;
		}
		// Argument reduction (reduce to interval [0,1]). If `x` is negative, we can safely negate the value, taking advantage of the error function being an odd function; i.e., `erf(-x) = -erf(x)`.
		if ( x < 0.0 ) {
			sign = -1.0;
			ax = -x;
		} else {
			sign = 1.0;
			ax = x;
		}
		q = 1.0 - ax;

		// |x| <= 0.5
		if ( ax <= 0.5 ) {
			g = ax * ( ax + 10.0 );
			r = rationalFcnR1( ax );
			return sign * ( (g*Y1) + (g*r) );
		}
		// 1-|x| >= 0.25
		if ( q >= 0.25 ) {
			g = sqrt( -2.0 * ln(q) );
			q -= 0.25;
			r = rationalFcnR2( q );
			return sign * ( g / (Y2+r) );
		}
		q = sqrt( -ln( q ) );

		// q < 3
		if ( q < 3.0 ) {
			qs = q - 1.125;
			r = rationalFcnR3( qs );
			return sign * ( (Y3*q) + (r*q) );
		}
		// q < 6
		if ( q < 6.0 ) {
			qs = q - 3.0;
			r = rationalFcnR4( qs );
			return sign * ( (Y4*q) + (r*q) );
		}
		// q < 18
		qs = q - 6.0;
		r = rationalFcnR5( qs );
		return sign * ( (Y5*q) + (r*q) );
	}


	// EXPORTS //

	main$3 = erfinv;
	return main$3;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$4;
var hasRequiredLib$4;

function requireLib$4 () {
	if (hasRequiredLib$4) return lib$4;
	hasRequiredLib$4 = 1;

	/**
	* Evaluate the inverse error function.
	*
	* @module @stdlib/math-base-special-erfinv
	*
	* @example
	* var erfinv = require( '@stdlib/math-base-special-erfinv' );
	*
	* var y = erfinv( 0.5 );
	* // returns ~0.4769
	*
	* y = erfinv( 0.8 );
	* // returns ~0.9062
	*
	* y = erfinv( 0.0 );
	* // returns 0.0
	*
	* y = erfinv( -0.0 );
	* // returns -0.0
	*
	* y = erfinv( -1.0 );
	* // returns -Infinity
	*
	* y = erfinv( 1.0 );
	* // returns Infinity
	*
	* y = erfinv( NaN );
	* // returns NaN
	*/

	// MODULES //

	var main = requireMain$3();


	// EXPORTS //

	lib$4 = main;
	return lib$4;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$2;
var hasRequiredMain$2;

function requireMain$2 () {
	if (hasRequiredMain$2) return main$2;
	hasRequiredMain$2 = 1;

	// MODULES //

	var erfinv = requireLib$4();
	var isnan = requireLib$1s();
	var sqrt = requireLib$I();


	// MAIN //

	/**
	* Evaluates the quantile function for a normal distribution with mean `mu` and standard deviation `sigma` at a probability `p`.
	*
	* @param {Probability} p - input value
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {number} evaluated quantile function
	*
	* @example
	* var y = quantile( 0.8, 0.0, 1.0 );
	* // returns ~0.842
	*
	* @example
	* var y = quantile( 0.5, 4.0, 2.0 );
	* // returns 4.0
	*
	* @example
	* var y = quantile( 1.1, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( -0.2, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( NaN, 0.0, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( 0.0, NaN, 1.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( 0.0, 0.0, NaN );
	* // returns NaN
	*
	* @example
	* // Negative standard deviation:
	* var y = quantile( 0.5, 0.0, -1.0 );
	* // returns NaN
	*/
	function quantile( p, mu, sigma ) {
		var A;
		var B;

		if (
			isnan( mu ) ||
			isnan( sigma ) ||
			isnan( p ) ||
			sigma < 0.0 ||
			p < 0.0 ||
			p > 1.0
		) {
			return NaN;
		}
		if ( sigma === 0.0 ) {
			return mu;
		}
		A = mu;
		B = sigma * sqrt( 2.0 );
		return A + (B * erfinv( (2.0*p) - 1.0 ));
	}


	// EXPORTS //

	main$2 = quantile;
	return main$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main$1;
var hasRequiredMain$1;

function requireMain$1 () {
	if (hasRequiredMain$1) return main$1;
	hasRequiredMain$1 = 1;

	// MODULES //

	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Evaluates the quantile function for a degenerate distribution centered at `mu`.
	*
	* @param {Probability} p - input value
	* @param {number} mu - constant value of the distribution
	* @returns {number} evaluated quantile function
	*
	* @example
	* var y = quantile( 0.5, 2.0 );
	* // returns 2.0
	*
	* @example
	* var y = quantile( 0.9, 4.0 );
	* // returns 4.0
	*
	* @example
	* var y = quantile( 1.1, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( -0.2, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( NaN, 0.0 );
	* // returns NaN
	*
	* @example
	* var y = quantile( 0.0, NaN );
	* // returns NaN
	*/
	function quantile( p, mu ) {
		if ( isnan( p ) || p < 0.0 || p > 1.0 ) {
			return NaN;
		}
		return mu;
	}


	// EXPORTS //

	main$1 = quantile;
	return main$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1$1;
var hasRequiredFactory$1;

function requireFactory$1 () {
	if (hasRequiredFactory$1) return factory_1$1;
	hasRequiredFactory$1 = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var isnan = requireLib$1s();


	// MAIN //

	/**
	* Returns a function for evaluating the quantile function of a degenerate distribution centered at a provided mean value.
	*
	* @param {number} mu - value at which to center the distribution
	* @returns {Function} function to evaluate the quantile function
	*
	* @example
	* var quantile = factory( 5.0 );
	*
	* var y = quantile( 0.3 );
	* // returns 5.0
	*
	* y = quantile( 0.1 );
	* // returns 5.0
	*
	* y = quantile( 1.1 );
	* // returns NaN
	*/
	function factory( mu ) {
		if ( isnan( mu ) ) {
			return constantFunction( NaN );
		}
		return quantile;

		/**
		* Evaluates the quantile function of a degenerate distribution.
		*
		* @private
		* @param {Probability} p - input value
		* @returns {number} evaluated quantile function
		*
		* @example
		* var y = quantile( 0.5 );
		* // returns <number>
		*/
		function quantile( p ) {
			if ( isnan( p ) || p < 0.0 || p > 1.0 ) {
				return NaN;
			}
			return mu;
		}
	}


	// EXPORTS //

	factory_1$1 = factory;
	return factory_1$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$3;
var hasRequiredLib$3;

function requireLib$3 () {
	if (hasRequiredLib$3) return lib$3;
	hasRequiredLib$3 = 1;

	/**
	* Degenerate distribution quantile function.
	*
	* @module @stdlib/stats-base-dists-degenerate-quantile
	*
	* @example
	* var quantile = require( '@stdlib/stats-base-dists-degenerate-quantile' );
	*
	* var y = quantile( 0.5, 2.0 );
	* // returns 2.0
	*
	* @example
	* var factory = require( '@stdlib/stats-base-dists-degenerate-quantile' ).factory;
	*
	* var quantile = factory( 10.0 );
	*
	* var y = quantile( 0.5 );
	* // returns 10.0
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$1();
	var factory = requireFactory$1();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$3 = main;
	return lib$3;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var factory_1;
var hasRequiredFactory;

function requireFactory () {
	if (hasRequiredFactory) return factory_1;
	hasRequiredFactory = 1;

	// MODULES //

	var constantFunction = requireLib$H();
	var degenerate = requireLib$3().factory;
	var erfinv = requireLib$4();
	var isnan = requireLib$1s();
	var sqrt = requireLib$I();


	// MAIN //

	/**
	* Returns a function for evaluating the quantile function of a normal distribution.
	*
	* @param {number} mu - mean
	* @param {NonNegativeNumber} sigma - standard deviation
	* @returns {Function} quantile function
	*
	* @example
	* var quantile = factory( 10.0, 2.0 );
	* var y = quantile( 0.5 );
	* // returns 10.0
	*
	* y = quantile( 0.8 );
	* // returns ~11.683
	*/
	function factory( mu, sigma ) {
		var A;
		var B;
		if ( isnan( mu ) || isnan( sigma ) || sigma < 0.0 ) {
			return constantFunction( NaN );
		}
		if ( sigma === 0.0 ) {
			degenerate( mu );
		}
		A = mu;
		B = sigma * sqrt( 2.0 );
		return quantile;

		/**
		* Evaluates the quantile function for a normal distribution.
		*
		* @private
		* @param {Probability} p - input value
		* @returns {number} evaluated quantile function
		*
		* @example
		* var y = quantile( 0.3 );
		* // returns <number>
		*/
		function quantile( p ) {
			if ( isnan( p ) || p < 0.0 || p > 1.0 ) {
				return NaN;
			}
			return A + ( B * erfinv( (2.0*p) - 1.0 ) );
		}
	}


	// EXPORTS //

	factory_1 = factory;
	return factory_1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$2;
var hasRequiredLib$2;

function requireLib$2 () {
	if (hasRequiredLib$2) return lib$2;
	hasRequiredLib$2 = 1;

	/**
	* Normal distribution quantile function.
	*
	* @module @stdlib/stats-base-dists-normal-quantile
	*
	* @example
	* var quantile = require( '@stdlib/stats-base-dists-normal-quantile' );
	*
	* var y = quantile( 0.8, 0.0, 1.0 );
	* // returns ~0.842
	*
	* var myQuantile = quantile.factory( 10.0, 2.0 );
	* y = myQuantile( 0.5 );
	* // returns 10.0
	*/

	// MODULES //

	var setReadOnly = requireLib$1t();
	var main = requireMain$2();
	var factory = requireFactory();


	// MAIN //

	setReadOnly( main, 'factory', factory );


	// EXPORTS //

	lib$2 = main;
	return lib$2;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var main;
var hasRequiredMain;

function requireMain () {
	if (hasRequiredMain) return main;
	hasRequiredMain = 1;

	// MODULES //

	var defineProperty = requireLib$1v();
	var setReadOnly = requireLib$1t();
	var setReadOnlyAccessor = requireLib$E();
	var isPositive = requireLib$C().isPrimitive;
	var isNumber = requireLib$D().isPrimitive;
	var isnan = requireLib$B();
	var entropy = requireLib$w();
	var kurtosis = requireLib$v();
	var mean = requireLib$u();
	var median = requireLib$t();
	var mode = requireLib$s();
	var skewness = requireLib$r();
	var stdev = requireLib$q();
	var variance = requireLib$p();
	var cdf = requireLib$F();
	var logcdf = requireLib$k();
	var logpdf = requireLib$a();
	var mgf = requireLib$7();
	var pdf = requireLib$5();
	var quantile = requireLib$2();
	var format = requireLib$1w();


	// FUNCTIONS //

	/**
	* Evaluates the cumulative distribution function (CDF).
	*
	* @private
	* @param {number} x - input value
	* @returns {Probability} evaluated CDF
	*/
	function normalCDF( x ) {
		return cdf( x, this.mu, this.sigma );
	}

	/**
	* Evaluates the natural logarithm of the cumulative distribution function (CDF).
	* @private
	* @param {number} x - input value
	* @returns {number} evaluated logCDF
	*/
	function normalLogCDF( x ) {
		return logcdf( x, this.mu, this.sigma );
	}

	/**
	* Evaluates the natural logarithm of the probability density function (PDF).
	*
	* @private
	* @param {number} x - input value
	* @returns {number} evaluated logPDF
	*/
	function normalLogPDF( x ) {
		return logpdf( x, this.mu, this.sigma );
	}

	/**
	* Evaluates the moment-generating function (MGF).
	*
	* @private
	* @param {number} t - input value
	* @returns {number} evaluated MGF
	*/
	function normalMGF( t ) {
		return mgf( t, this.mu, this.sigma );
	}

	/**
	* Evaluates the probability density function (PDF).
	*
	* @private
	* @param {number} x - input value
	* @returns {number} evaluated PDF
	*/
	function normalPDF( x ) {
		return pdf( x, this.mu, this.sigma );
	}

	/**
	* Evaluates the quantile function.
	*
	* @private
	* @param {Probability} p - input probability
	* @returns {number} evaluated quantile function
	*/
	function normalQuantile( p ) {
		return quantile( p, this.mu, this.sigma );
	}


	// MAIN //

	/**
	* Normal distribution constructor.
	*
	* @constructor
	* @param {number} [mu=0.0] - location parameter
	* @param {PositiveNumber} [sigma=1.0] - scale parameter
	* @throws {TypeError} `sigma` must be a positive number
	* @returns {Normal} distribution instance
	*
	* @example
	* var normal = new Normal( 1.0, 1.0 );
	*
	* var y = normal.cdf( 1.5 );
	* // returns ~0.691
	*
	* var mu = normal.mean;
	* // returns 1.0
	*/
	function Normal() {
		var sigma;
		var mu;
		if ( !(this instanceof Normal) ) {
			if ( arguments.length === 0 ) {
				return new Normal();
			}
			return new Normal( arguments[ 0 ], arguments[ 1 ] );
		}
		if ( arguments.length ) {
			mu = arguments[ 0 ];
			sigma = arguments[ 1 ];
			if ( !isNumber( mu ) || isnan( mu ) ) {
				throw new TypeError( format( 'invalid argument. Location parameter must be a number. Value: `%s`.', mu ) );
			}
			if ( !isPositive( sigma ) ) {
				throw new TypeError( format( 'invalid argument. Scale parameter must be a positive number. Value: `%s`.', sigma ) );
			}
		} else {
			mu = 0.0;
			sigma = 1.0;
		}
		defineProperty( this, 'mu', {
			'configurable': false,
			'enumerable': true,
			'get': function get() {
				return mu;
			},
			'set': function set( value ) {
				if ( !isNumber( value ) || isnan( value ) ) {
					throw new TypeError( format( 'invalid assignment. Must be a number. Value: `%s`.', value ) );
				}
				mu = value;
			}
		});
		defineProperty( this, 'sigma', {
			'configurable': false,
			'enumerable': true,
			'get': function get() {
				return sigma;
			},
			'set': function set( value ) {
				if ( !isPositive( value ) ) {
					throw new TypeError( format( 'invalid assignment. Must be a positive number. Value: `%s`.', value ) );
				}
				sigma = value;
			}
		});
		return this;
	}

	/**
	* Normal distribution differential entropy.
	*
	* @name entropy
	* @memberof Normal.prototype
	* @type {number}
	* @see [differential entropy]{@link https://en.wikipedia.org/wiki/Entropy_%28information_theory%29}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.entropy;
	* // returns ~3.904
	*/
	setReadOnlyAccessor( Normal.prototype, 'entropy', function get() {
		return entropy( this.mu, this.sigma );
	});

	/**
	* Normal distribution excess kurtosis.
	*
	* @name kurtosis
	* @memberof Normal.prototype
	* @type {number}
	* @see [kurtosis]{@link https://en.wikipedia.org/wiki/Kurtosis}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.kurtosis;
	* // returns 0.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'kurtosis', function get() {
		return kurtosis( this.mu, this.sigma );
	});

	/**
	* Normal distribution expected value.
	*
	* @name mean
	* @memberof Normal.prototype
	* @type {number}
	* @see [expected value]{@link https://en.wikipedia.org/wiki/Expected_value}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.mean;
	* // returns 4.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'mean', function get() {
		return mean( this.mu, this.sigma );
	});

	/**
	* Normal distribution median.
	*
	* @name median
	* @memberof Normal.prototype
	* @type {number}
	* @see [median]{@link https://en.wikipedia.org/wiki/Median}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.median;
	* // returns 4.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'median', function get() {
		return median( this.mu, this.sigma );
	});

	/**
	* Normal distribution mode.
	*
	* @name mode
	* @memberof Normal.prototype
	* @type {number}
	* @see [mode]{@link https://en.wikipedia.org/wiki/Mode_%28statistics%29}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.mode;
	* // returns 4.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'mode', function get() {
		return mode( this.mu, this.sigma );
	});

	/**
	* Normal distribution skewness.
	*
	* @name skewness
	* @memberof Normal.prototype
	* @type {number}
	* @see [skewness]{@link https://en.wikipedia.org/wiki/Skewness}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.skewness;
	* // returns 0.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'skewness', function get() {
		return skewness( this.mu, this.sigma );
	});

	/**
	* Normal distribution standard deviation.
	*
	* @name stdev
	* @memberof Normal.prototype
	* @type {PositiveNumber}
	* @see [standard deviation]{@link https://en.wikipedia.org/wiki/Standard_deviation}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.stdev;
	* // returns 12.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'stdev', function get() {
		return stdev( this.mu, this.sigma );
	});

	/**
	* Normal distribution variance.
	*
	* @name variance
	* @memberof Normal.prototype
	* @type {PositiveNumber}
	* @see [variance]{@link https://en.wikipedia.org/wiki/Variance}
	*
	* @example
	* var normal = new Normal( 4.0, 12.0 );
	*
	* var v = normal.variance;
	* // returns 144.0
	*/
	setReadOnlyAccessor( Normal.prototype, 'variance', function get() {
		return variance( this.mu, this.sigma );
	});

	/**
	* Evaluates the cumulative distribution function (CDF).
	*
	* @name cdf
	* @memberof Normal.prototype
	* @type {Function}
	* @param {number} x - input value
	* @returns {number} evaluated CDF
	* @see [cdf]{@link https://en.wikipedia.org/wiki/Cumulative_distribution_function}
	*
	* @example
	* var normal = new Normal( 2.0, 4.0 );
	*
	* var v = normal.cdf( 0.5 );
	* // returns ~0.354
	*/
	setReadOnly( Normal.prototype, 'cdf', normalCDF );

	/**
	* Evaluates the natural logarithm of the cumulative distribution function (CDF).
	*
	* @name logcdf
	* @memberof Normal.prototype
	* @type {Function}
	* @param {number} x - input value
	* @returns {number} evaluated logarithm of the CDF
	* @see [cdf]{@link https://en.wikipedia.org/wiki/Cumulative_distribution_function}
	*
	* @example
	* var normal = new Normal( 2.0, 4.0 );
	*
	* var v = normal.logcdf( 0.5 );
	* // returns ~-1.039
	*/
	setReadOnly( Normal.prototype, 'logcdf', normalLogCDF );

	/**
	* Evaluates the natural logarithm of the probability density function (PDF).
	*
	* @name logpdf
	* @memberof Normal.prototype
	* @type {Function}
	* @param {number} x - input value
	* @returns {number} evaluated logPDF
	* @see [pdf]{@link https://en.wikipedia.org/wiki/Probability_density_function}
	*
	* @example
	* var normal = new Normal( 2.0, 4.0 );
	*
	* var v = normal.logpdf( 0.8 );
	* // returns ~-2.35
	*/
	setReadOnly( Normal.prototype, 'logpdf', normalLogPDF );

	/**
	* Evaluates the moment-generating function (MGF).
	*
	* @name mgf
	* @memberof Normal.prototype
	* @type {Function}
	* @param {number} t - input value
	* @returns {number} evaluated MGF
	* @see [mgf]{@link https://en.wikipedia.org/wiki/Moment-generating_function}
	*
	* @example
	* var normal = new Normal( 2.0, 4.0 );
	*
	* var v = normal.mgf( 0.01 );
	* // returns ~1.021
	*/
	setReadOnly( Normal.prototype, 'mgf', normalMGF );

	/**
	* Evaluates the probability density function (PDF).
	*
	* @name pdf
	* @memberof Normal.prototype
	* @type {Function}
	* @param {number} x - input value
	* @returns {number} evaluated PDF
	* @see [pdf]{@link https://en.wikipedia.org/wiki/Probability_density_function}
	*
	* @example
	* var normal = new Normal( 2.0, 4.0 );
	*
	* var v = normal.pdf( 0.8 );
	* // returns ~0.095
	*/
	setReadOnly( Normal.prototype, 'pdf', normalPDF );

	/**
	* Evaluates the quantile function.
	*
	* @name quantile
	* @memberof Normal.prototype
	* @type {Function}
	* @param {Probability} p - input probability
	* @returns {number} evaluated quantile function
	* @see [quantile function]{@link https://en.wikipedia.org/wiki/Quantile_function}
	*
	* @example
	* var normal = new Normal( 2.0, 4.0 );
	*
	* var v = normal.quantile( 0.5 );
	* // returns 2.0
	*/
	setReadOnly( Normal.prototype, 'quantile', normalQuantile );


	// EXPORTS //

	main = Normal;
	return main;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib$1;
var hasRequiredLib$1;

function requireLib$1 () {
	if (hasRequiredLib$1) return lib$1;
	hasRequiredLib$1 = 1;

	/**
	* Normal distribution constructor.
	*
	* @module @stdlib/stats-base-dists-normal-ctor
	*
	* @example
	* var Normal = require( '@stdlib/stats-base-dists-normal-ctor' );
	*
	* var normal = new Normal( 1.0, 1.0 );
	*
	* var y = normal.cdf( 1.5 );
	* // returns ~0.691
	*
	* var mu = normal.mean;
	* // returns 1.0
	*/

	// MODULES //

	var main = requireMain();


	// EXPORTS //

	lib$1 = main;
	return lib$1;
}

/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

var lib;
var hasRequiredLib;

function requireLib () {
	if (hasRequiredLib) return lib;
	hasRequiredLib = 1;

	/*
	* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
	*/

	// MODULES //

	var setReadOnly = requireLib$1u();


	// MAIN //

	/**
	* Top-level namespace.
	*
	* @namespace normal
	*/
	var normal = {};

	/**
	* @name cdf
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/cdf}
	*/
	setReadOnly( normal, 'cdf', requireLib$F() );

	/**
	* @name Normal
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/ctor}
	*/
	setReadOnly( normal, 'Normal', requireLib$1() );

	/**
	* @name entropy
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/entropy}
	*/
	setReadOnly( normal, 'entropy', requireLib$w() );

	/**
	* @name kurtosis
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/kurtosis}
	*/
	setReadOnly( normal, 'kurtosis', requireLib$v() );

	/**
	* @name logcdf
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/logcdf}
	*/
	setReadOnly( normal, 'logcdf', requireLib$k() );

	/**
	* @name logpdf
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/logpdf}
	*/
	setReadOnly( normal, 'logpdf', requireLib$a() );

	/**
	* @name mean
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/mean}
	*/
	setReadOnly( normal, 'mean', requireLib$u() );

	/**
	* @name median
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/median}
	*/
	setReadOnly( normal, 'median', requireLib$t() );

	/**
	* @name mgf
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/mgf}
	*/
	setReadOnly( normal, 'mgf', requireLib$7() );

	/**
	* @name mode
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/mode}
	*/
	setReadOnly( normal, 'mode', requireLib$s() );

	/**
	* @name pdf
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/pdf}
	*/
	setReadOnly( normal, 'pdf', requireLib$5() );

	/**
	* @name quantile
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/quantile}
	*/
	setReadOnly( normal, 'quantile', requireLib$2() );

	/**
	* @name skewness
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/skewness}
	*/
	setReadOnly( normal, 'skewness', requireLib$r() );

	/**
	* @name stdev
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/stdev}
	*/
	setReadOnly( normal, 'stdev', requireLib$q() );

	/**
	* @name variance
	* @memberof normal
	* @readonly
	* @type {Function}
	* @see {@link module:@stdlib/stats/base/dists/normal/variance}
	*/
	setReadOnly( normal, 'variance', requireLib$p() );


	// EXPORTS //

	lib = normal;
	return lib;
}

var hasRequiredStatisticsFormulas;

function requireStatisticsFormulas () {
	if (hasRequiredStatisticsFormulas) return statisticsFormulas;
	hasRequiredStatisticsFormulas = 1;
	Object.defineProperty(statisticsFormulas, "__esModule", { value: true });
	statisticsFormulas.roundTo = roundTo;
	statisticsFormulas.mean = mean;
	statisticsFormulas.variance = variance;
	statisticsFormulas.standardDeviation = standardDeviation;
	statisticsFormulas.pValue = pValue;
	statisticsFormulas.calculateStatisticalSignificance = calculateStatisticalSignificance;
	statisticsFormulas.calculatePairedSignificance = calculatePairedSignificance;
	statisticsFormulas.calculateWinRateCI = calculateWinRateCI;
	statisticsFormulas.applyBenjaminiHochberg = applyBenjaminiHochberg;
	statisticsFormulas.bootstrapMeanCI = bootstrapMeanCI;
	const lodash_1 = requireLodash();
	const stats_base_dists_normal_1 = requireLib();
	const statistical_significance_rating_1 = requireStatisticalSignificanceRating();
	const Z_95 = 1.96;
	function roundTo(value, numDigits) {
	    return (0, lodash_1.round)(value, numDigits);
	}
	function mean({ values, numDigits = 3 }) {
	    if (values.length === 0) {
	        return 0;
	    }
	    const sum = values.reduce((acc, val) => acc + val, 0);
	    const result = sum / values.length;
	    return (0, lodash_1.round)(result, numDigits);
	}
	function variance({ values, numDigits = 3 }) {
	    if (values.length < 2) {
	        return 0;
	    }
	    const meanValue = mean({ values, numDigits: 10 });
	    const squaredDifferences = values.map((val) => Math.pow(val - meanValue, 2));
	    const sumSquaredDifferences = squaredDifferences.reduce((acc, val) => acc + val, 0);
	    const result = sumSquaredDifferences / (values.length - 1);
	    return (0, lodash_1.round)(result, numDigits);
	}
	function standardDeviation({ values, numDigits = 3 }) {
	    if (values.length < 2) {
	        return 0;
	    }
	    const varianceValue = variance({ values, numDigits: 10 });
	    const result = Math.sqrt(varianceValue);
	    return (0, lodash_1.round)(result, numDigits);
	}
	function pValue(aMean, aStdDev, bMean, bStdDev, twoSided = false) {
	    const difference = aMean - bMean;
	    const standardError = Math.sqrt(aStdDev * aStdDev + bStdDev * bStdDev);
	    const zScore = difference / standardError;
	    if (twoSided) {
	        return 2 * (1 - (0, stats_base_dists_normal_1.cdf)(Math.abs(zScore), 0, 1));
	    }
	    else {
	        return zScore > 0 ?
	            1 - (0, stats_base_dists_normal_1.cdf)(zScore, 0, 1)
	            : (0, stats_base_dists_normal_1.cdf)(zScore, 0, 1);
	    }
	}
	function calculateStatisticalSignificance(value1, value2) {
	    const _pValue = ((value1.standardDeviation !== undefined && value1.standardDeviation > 0) ||
	        (value2.standardDeviation !== undefined && value2.standardDeviation > 0)) ?
	        pValue(value1.value, value1.standardDeviation ?? 0, value2.value, value2.standardDeviation ?? 0)
	        : null;
	    const significance = _pValue === null ? null
	        : _pValue <= 0.01 ? statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.verySignificant
	            : _pValue <= 0.05 ? statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.significant
	                : _pValue <= 0.157 ? statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.suggestive
	                    : statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.indistinctFromNoise;
	    return { pValue: _pValue, significance };
	}
	function calculatePairedSignificance(deltas) {
	    const n = deltas.length;
	    if (n < 2) {
	        return { meanDelta: deltas[0] ?? 0, stdDev: 0, n, pValue: null, significance: null };
	    }
	    const meanDelta = mean({ values: deltas });
	    const stdDev = standardDeviation({ values: deltas });
	    const se = stdDev / Math.sqrt(n);
	    const { pValue: p, significance } = calculateStatisticalSignificance({ value: meanDelta, standardDeviation: se }, { value: 0 });
	    return { meanDelta, stdDev, n, pValue: p, significance };
	}
	function calculateWinRateCI(wins, losses, n) {
	    if (n === 0) {
	        return null;
	    }
	    const winRate = (0, lodash_1.round)(wins / n, 4);
	    const lossRate = (0, lodash_1.round)(losses / n, 4);
	    const tieRate = (0, lodash_1.round)(1 - winRate - lossRate, 4);
	    const z2 = Z_95 * Z_95;
	    const center = (wins + z2 / 2) / (n + z2);
	    const margin = (Z_95 * Math.sqrt((wins * (n - wins)) / n + z2 / 4)) / (n + z2);
	    return {
	        winRate,
	        lossRate,
	        tieRate,
	        ciLow: (0, lodash_1.round)(Math.max(0, center - margin), 4),
	        ciHigh: (0, lodash_1.round)(Math.min(1, center + margin), 4),
	        n,
	    };
	}
	function applyBenjaminiHochberg(rawPValues) {
	    const m = rawPValues.length;
	    if (m === 0) {
	        return [];
	    }
	    const indexed = rawPValues.map((p, i) => ({ p, i }));
	    indexed.sort((a, b) => a.p - b.p);
	    const adjusted = new Array(m);
	    for (let rank = 0; rank < m; rank++) {
	        adjusted[rank] = Math.min(1, (indexed[rank].p * m) / (rank + 1));
	    }
	    for (let rank = m - 2; rank >= 0; rank--) {
	        adjusted[rank] = Math.min(adjusted[rank], adjusted[rank + 1]);
	    }
	    const result = new Array(m);
	    for (let rank = 0; rank < m; rank++) {
	        result[indexed[rank].i] = (0, lodash_1.round)(adjusted[rank], 6);
	    }
	    return result;
	}
	function bootstrapMeanCI(values, opts) {
	    if (values.length < 2) {
	        return null;
	    }
	    const resamples = opts?.resamples ?? 1000;
	    const alpha = opts?.alpha ?? 0.05;
	    const n = values.length;
	    const means = [];
	    for (let b = 0; b < resamples; b++) {
	        let sum = 0;
	        for (let j = 0; j < n; j++) {
	            sum += values[Math.floor(Math.random() * n)];
	        }
	        means.push(sum / n);
	    }
	    means.sort((a, b) => a - b);
	    const lo = Math.floor((alpha / 2) * resamples);
	    const hi = Math.ceil((1 - alpha / 2) * resamples) - 1;
	    return {
	        mean: (0, lodash_1.round)(values.reduce((s, v) => s + v, 0) / n, 4),
	        ciLow: (0, lodash_1.round)(means[lo], 4),
	        ciHigh: (0, lodash_1.round)(means[hi], 4),
	    };
	}
	
	return statisticsFormulas;
}

var hasRequiredFormulas;

function requireFormulas () {
	if (hasRequiredFormulas) return formulas;
	hasRequiredFormulas = 1;
	(function (exports) {
		var __createBinding = (formulas && formulas.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (formulas && formulas.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireMetricsFormulas(), exports);
		__exportStar(requireStatisticsFormulas(), exports);
		
	} (formulas));
	return formulas;
}

var hasRequiredDataScience;

function requireDataScience () {
	if (hasRequiredDataScience) return DataScience;
	hasRequiredDataScience = 1;
	(function (exports) {
		var __createBinding = (DataScience && DataScience.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (DataScience && DataScience.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DataScienceFormulas = void 0;
		__exportStar(requireModelCards(), exports);
		__exportStar(requireAiModelConfig(), exports);
		__exportStar(requireBoundaryConditionsDenormalized(), exports);
		__exportStar(requireSignalAttributesDenormalized(), exports);
		__exportStar(requireSignalDriveCharacteristicsDenormalized(), exports);
		__exportStar(requireSignalTiesDenormalized(), exports);
		__exportStar(requireSignalTiesDenormalized_validation(), exports);
		__exportStar(requireSignalFloatBehaviourDenormalized(), exports);
		__exportStar(requireConditionModeDenormalized(), exports);
		__exportStar(requireComponentFunctionDenormalized(), exports);
		__exportStar(requireSignalIdOrphanPruning(), exports);
		__exportStar(requireBoundaryConditionsNormalized(), exports);
		__exportStar(requirePinMappingDenormalized(), exports);
		__exportStar(requireStatisticalSignificanceRating(), exports);
		__exportStar(requireValueWithStandardDeviation(), exports);
		__exportStar(requireActiveLowDenormalized(), exports);
		__exportStar(requireAdeIrDenormalized(), exports);
		__exportStar(requireInferredPackageProperties(), exports);
		__exportStar(requireModuleMetadataClassification(), exports);
		__exportStar(requireSignalNameNormalization(), exports);
		__exportStar(requireNormalizeSignalName(), exports);
		__exportStar(requireSignalNameCanonicalization(), exports);
		__exportStar(requireCadstromTextCodec(), exports);
		__exportStar(requireDisagreementReport(), exports);
		__exportStar(requireExperiments(), exports);
		exports.DataScienceFormulas = requireFormulas();
		
	} (DataScience));
	return DataScience;
}

var DocumentRetrieval = {};

var presets = {};

var ocrPresets = {};

var hasRequiredOcrPresets;

function requireOcrPresets () {
	if (hasRequiredOcrPresets) return ocrPresets;
	hasRequiredOcrPresets = 1;
	Object.defineProperty(ocrPresets, "__esModule", { value: true });
	ocrPresets.OcrPresetNameSchema = ocrPresets.OcrPresetName = void 0;
	ocrPresets.isOcrPresetName = isOcrPresetName;
	const zod_1 = /*@__PURE__*/ requireZod();
	var OcrPresetName;
	(function (OcrPresetName) {
	    OcrPresetName["AzureOcr"] = "azure-ocr";
	})(OcrPresetName || (ocrPresets.OcrPresetName = OcrPresetName = {}));
	ocrPresets.OcrPresetNameSchema = zod_1.z.nativeEnum(OcrPresetName);
	function isOcrPresetName(name) {
	    return Object.values(OcrPresetName).includes(name);
	}
	
	return ocrPresets;
}

var chunkingPresets = {};

var hasRequiredChunkingPresets;

function requireChunkingPresets () {
	if (hasRequiredChunkingPresets) return chunkingPresets;
	hasRequiredChunkingPresets = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ChunkingPresetNameSchema = exports.CHUNKING_PRESETS = exports.ChunkingMethod = exports.ChunkingProvider = exports.ChunkingPresetName = void 0;
		exports.isChunkingPresetName = isChunkingPresetName;
		exports.getChunkingPreset = getChunkingPreset;
		const zod_1 = /*@__PURE__*/ requireZod();
		var ChunkingPresetName;
		(function (ChunkingPresetName) {
		    ChunkingPresetName["LCRecursiveSize1000Overlap200"] = "LCRecursiveSize1000Overlap200";
		})(ChunkingPresetName || (exports.ChunkingPresetName = ChunkingPresetName = {}));
		var ChunkingProvider;
		(function (ChunkingProvider) {
		    ChunkingProvider["LangChain"] = "LangChain";
		})(ChunkingProvider || (exports.ChunkingProvider = ChunkingProvider = {}));
		var ChunkingMethod;
		(function (ChunkingMethod) {
		    ChunkingMethod["RecursiveCharacterTextSplitter"] = "RecursiveCharacterTextSplitter";
		})(ChunkingMethod || (exports.ChunkingMethod = ChunkingMethod = {}));
		exports.CHUNKING_PRESETS = {
		    [ChunkingPresetName.LCRecursiveSize1000Overlap200]: {
		        displayName: 'LangChain Recursive - Size 1000 Overlap 200',
		        provider: ChunkingProvider.LangChain,
		        method: ChunkingMethod.RecursiveCharacterTextSplitter,
		        chunkSize: 1000,
		        chunkOverlap: 200,
		        providerOptions: {
		            separators: ['\n## ', '\n### ', '\n#### ', '\n\n', '\n', ' '],
		        },
		    },
		};
		exports.ChunkingPresetNameSchema = zod_1.z.nativeEnum(ChunkingPresetName);
		function isChunkingPresetName(name) {
		    return Object.values(ChunkingPresetName).includes(name);
		}
		function getChunkingPreset(name) {
		    return exports.CHUNKING_PRESETS[name];
		}
		
	} (chunkingPresets));
	return chunkingPresets;
}

var embeddingPresets = {};

var hasRequiredEmbeddingPresets;

function requireEmbeddingPresets () {
	if (hasRequiredEmbeddingPresets) return embeddingPresets;
	hasRequiredEmbeddingPresets = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.EmbeddingPresetNameSchema = exports.EMBEDDING_PRESETS = exports.EmbeddingDimension = exports.EmbeddingProvider = exports.EmbeddingPresetName = void 0;
		exports.isEmbeddingPresetName = isEmbeddingPresetName;
		exports.getEmbeddingPreset = getEmbeddingPreset;
		const zod_1 = /*@__PURE__*/ requireZod();
		var EmbeddingPresetName;
		(function (EmbeddingPresetName) {
		    EmbeddingPresetName["Openai3Large1024"] = "openai-3-large-1024";
		    EmbeddingPresetName["Openai3Small1536"] = "openai-3-small-1536";
		    EmbeddingPresetName["TitanV2_512"] = "titan-v2-512";
		    EmbeddingPresetName["TitanV2_1024"] = "titan-v2-1024";
		})(EmbeddingPresetName || (exports.EmbeddingPresetName = EmbeddingPresetName = {}));
		var EmbeddingProvider;
		(function (EmbeddingProvider) {
		    EmbeddingProvider["OpenAI"] = "OpenAI";
		    EmbeddingProvider["AWSBedrock"] = "AWSBedrock";
		})(EmbeddingProvider || (exports.EmbeddingProvider = EmbeddingProvider = {}));
		var EmbeddingDimension;
		(function (EmbeddingDimension) {
		    EmbeddingDimension[EmbeddingDimension["Dim256"] = 256] = "Dim256";
		    EmbeddingDimension[EmbeddingDimension["Dim512"] = 512] = "Dim512";
		    EmbeddingDimension[EmbeddingDimension["Dim1024"] = 1024] = "Dim1024";
		    EmbeddingDimension[EmbeddingDimension["Dim1536"] = 1536] = "Dim1536";
		})(EmbeddingDimension || (exports.EmbeddingDimension = EmbeddingDimension = {}));
		exports.EMBEDDING_PRESETS = {
		    [EmbeddingPresetName.Openai3Large1024]: {
		        displayName: 'OpenAI text-embedding-3-large (reduced to 1024)',
		        provider: EmbeddingProvider.OpenAI,
		        modelId: 'text-embedding-3-large',
		        dimensions: EmbeddingDimension.Dim1024,
		        providerOptions: {},
		    },
		    [EmbeddingPresetName.Openai3Small1536]: {
		        displayName: 'OpenAI text-embedding-3-small (1536)',
		        provider: EmbeddingProvider.OpenAI,
		        modelId: 'text-embedding-3-small',
		        dimensions: EmbeddingDimension.Dim1536,
		        providerOptions: {},
		    },
		    [EmbeddingPresetName.TitanV2_512]: {
		        displayName: 'AWS Titan Text Embeddings V2 (512)',
		        provider: EmbeddingProvider.AWSBedrock,
		        modelId: 'amazon.titan-embed-text-v2:0',
		        dimensions: EmbeddingDimension.Dim512,
		        providerOptions: {
		            normalize: true,
		        },
		    },
		    [EmbeddingPresetName.TitanV2_1024]: {
		        displayName: 'AWS Titan Text Embeddings V2 (1024)',
		        provider: EmbeddingProvider.AWSBedrock,
		        modelId: 'amazon.titan-embed-text-v2:0',
		        dimensions: EmbeddingDimension.Dim1024,
		        providerOptions: {
		            normalize: true,
		        },
		    },
		};
		exports.EmbeddingPresetNameSchema = zod_1.z.nativeEnum(EmbeddingPresetName);
		function isEmbeddingPresetName(name) {
		    return Object.values(EmbeddingPresetName).includes(name);
		}
		function getEmbeddingPreset(name) {
		    return exports.EMBEDDING_PRESETS[name];
		}
		
	} (embeddingPresets));
	return embeddingPresets;
}

var hasRequiredPresets;

function requirePresets () {
	if (hasRequiredPresets) return presets;
	hasRequiredPresets = 1;
	(function (exports) {
		var __createBinding = (presets && presets.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (presets && presets.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireOcrPresets(), exports);
		__exportStar(requireChunkingPresets(), exports);
		__exportStar(requireEmbeddingPresets(), exports);
		
	} (presets));
	return presets;
}

var searchIndexes = {};

var hasRequiredSearchIndexes;

function requireSearchIndexes () {
	if (hasRequiredSearchIndexes) return searchIndexes;
	hasRequiredSearchIndexes = 1;
	Object.defineProperty(searchIndexes, "__esModule", { value: true });
	searchIndexes.LexicalIndexNameSchema = searchIndexes.LexicalIndexName = void 0;
	searchIndexes.isLexicalIndexName = isLexicalIndexName;
	const zod_1 = /*@__PURE__*/ requireZod();
	var LexicalIndexName;
	(function (LexicalIndexName) {
	    LexicalIndexName["EnglishLexicalIndex"] = "lexical-english";
	    LexicalIndexName["StandardLexicalIndex"] = "lexical-standard";
	})(LexicalIndexName || (searchIndexes.LexicalIndexName = LexicalIndexName = {}));
	searchIndexes.LexicalIndexNameSchema = zod_1.z.nativeEnum(LexicalIndexName);
	function isLexicalIndexName(name) {
	    return Object.values(LexicalIndexName).includes(name);
	}
	
	return searchIndexes;
}

var searchOptions = {};

var hasRequiredSearchOptions;

function requireSearchOptions () {
	if (hasRequiredSearchOptions) return searchOptions;
	hasRequiredSearchOptions = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.HybridSearchOptionsSchema = exports.LexicalSearchOptionsSchema = exports.SearchOperatorSchema = exports.SearchOperator = exports.LexicalQueryTypeSchema = exports.LexicalQueryType = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		var LexicalQueryType;
		(function (LexicalQueryType) {
		    LexicalQueryType["Match"] = "match";
		    LexicalQueryType["Phrase"] = "phrase";
		    LexicalQueryType["SimpleQueryString"] = "simple_query_string";
		})(LexicalQueryType || (exports.LexicalQueryType = LexicalQueryType = {}));
		exports.LexicalQueryTypeSchema = zod_1.z.nativeEnum(LexicalQueryType);
		var SearchOperator;
		(function (SearchOperator) {
		    SearchOperator["Or"] = "or";
		    SearchOperator["And"] = "and";
		})(SearchOperator || (exports.SearchOperator = SearchOperator = {}));
		exports.SearchOperatorSchema = zod_1.z.nativeEnum(SearchOperator);
		const MatchQueryOptionsSchema = zod_1.z.object({
		    type: zod_1.z.literal(LexicalQueryType.Match),
		    operator: exports.SearchOperatorSchema.optional(),
		    fuzziness: zod_1.z.union([zod_1.z.literal('AUTO'), zod_1.z.literal(0), zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(false)]).optional(),
		    minimumShouldMatch: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		    analyzer: zod_1.z.string().optional(),
		});
		const MatchPhraseQueryOptionsSchema = zod_1.z.object({
		    type: zod_1.z.literal(LexicalQueryType.Phrase),
		    slop: zod_1.z.number().optional(),
		    analyzer: zod_1.z.string().optional(),
		});
		const SimpleQueryStringOptionsSchema = zod_1.z.object({
		    type: zod_1.z.literal(LexicalQueryType.SimpleQueryString),
		    defaultOperator: exports.SearchOperatorSchema.optional(),
		    flags: zod_1.z.string().optional(),
		    analyzer: zod_1.z.string().optional(),
		    minimumShouldMatch: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
		});
		exports.LexicalSearchOptionsSchema = zod_1.z.discriminatedUnion('type', [
		    MatchQueryOptionsSchema,
		    MatchPhraseQueryOptionsSchema,
		    SimpleQueryStringOptionsSchema,
		]);
		exports.HybridSearchOptionsSchema = zod_1.z.object({
		    semanticWeight: zod_1.z.number().min(0).max(1).optional(),
		    lexicalWeight: zod_1.z.number().min(0).max(1).optional(),
		    minScore: zod_1.z.number().optional(),
		    lexicalOptions: exports.LexicalSearchOptionsSchema.optional(),
		});
		
	} (searchOptions));
	return searchOptions;
}

var queryFusion = {};

var hasRequiredQueryFusion;

function requireQueryFusion () {
	if (hasRequiredQueryFusion) return queryFusion;
	hasRequiredQueryFusion = 1;
	Object.defineProperty(queryFusion, "__esModule", { value: true });
	queryFusion.FusionStrategySchema = queryFusion.FusionStrategy = queryFusion.ScoreMergeStrategySchema = queryFusion.ScoreMergeStrategy = queryFusion.QueryTypeSchema = queryFusion.QueryType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var QueryType;
	(function (QueryType) {
	    QueryType["Lexical"] = "lexical";
	    QueryType["Vector"] = "vector";
	    QueryType["Hybrid"] = "hybrid";
	})(QueryType || (queryFusion.QueryType = QueryType = {}));
	queryFusion.QueryTypeSchema = zod_1.z.nativeEnum(QueryType);
	var ScoreMergeStrategy;
	(function (ScoreMergeStrategy) {
	    ScoreMergeStrategy["Max"] = "max";
	    ScoreMergeStrategy["Sum"] = "sum";
	    ScoreMergeStrategy["MNZ"] = "mnz";
	    ScoreMergeStrategy["First"] = "first";
	})(ScoreMergeStrategy || (queryFusion.ScoreMergeStrategy = ScoreMergeStrategy = {}));
	queryFusion.ScoreMergeStrategySchema = zod_1.z.nativeEnum(ScoreMergeStrategy);
	var FusionStrategy;
	(function (FusionStrategy) {
	    FusionStrategy["Pooling"] = "pooling";
	    FusionStrategy["RRF"] = "rrf";
	    FusionStrategy["Interleave"] = "interleave";
	})(FusionStrategy || (queryFusion.FusionStrategy = FusionStrategy = {}));
	queryFusion.FusionStrategySchema = zod_1.z.nativeEnum(FusionStrategy);
	
	return queryFusion;
}

var searchProviders = {};

var hasRequiredSearchProviders;

function requireSearchProviders () {
	if (hasRequiredSearchProviders) return searchProviders;
	hasRequiredSearchProviders = 1;
	Object.defineProperty(searchProviders, "__esModule", { value: true });
	searchProviders.SearchProviderNameSchema = searchProviders.SearchProviderName = void 0;
	searchProviders.isSearchProviderName = isSearchProviderName;
	const zod_1 = /*@__PURE__*/ requireZod();
	var SearchProviderName;
	(function (SearchProviderName) {
	    SearchProviderName["OpenSearch"] = "opensearch";
	})(SearchProviderName || (searchProviders.SearchProviderName = SearchProviderName = {}));
	searchProviders.SearchProviderNameSchema = zod_1.z.nativeEnum(SearchProviderName);
	function isSearchProviderName(name) {
	    return Object.values(SearchProviderName).includes(name);
	}
	
	return searchProviders;
}

var documentSpecifications = {};

var hasRequiredDocumentSpecifications;

function requireDocumentSpecifications () {
	if (hasRequiredDocumentSpecifications) return documentSpecifications;
	hasRequiredDocumentSpecifications = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DocumentEmbeddingSpecificationSchema = exports.DocumentChunkingSpecificationSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const presets_1 = requirePresets();
		exports.DocumentChunkingSpecificationSchema = zod_1.z.object({
		    fileId: zod_1.z.string(),
		    ocrPresetName: presets_1.OcrPresetNameSchema,
		    chunkingPresetName: presets_1.ChunkingPresetNameSchema,
		});
		exports.DocumentEmbeddingSpecificationSchema = exports.DocumentChunkingSpecificationSchema.extend({
		    embeddingPresetName: presets_1.EmbeddingPresetNameSchema,
		});
		
	} (documentSpecifications));
	return documentSpecifications;
}

var chunks = {};

var hasRequiredChunks;

function requireChunks () {
	if (hasRequiredChunks) return chunks;
	hasRequiredChunks = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ChunkContentWithEmbeddingSchema = exports.ChunkContentSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.ChunkContentSchema = zod_1.z.object({
		    chunkId: zod_1.z.string(),
		    textContent: zod_1.z.string(),
		    chunkIndex: zod_1.z.number(),
		    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
		});
		exports.ChunkContentWithEmbeddingSchema = exports.ChunkContentSchema.extend({
		    embedding: zod_1.z.array(zod_1.z.number()),
		});
		
	} (chunks));
	return chunks;
}

var chunkedDocument = {};

var hasRequiredChunkedDocument;

function requireChunkedDocument () {
	if (hasRequiredChunkedDocument) return chunkedDocument;
	hasRequiredChunkedDocument = 1;
	Object.defineProperty(chunkedDocument, "__esModule", { value: true });
	chunkedDocument.EmbeddedDocumentSchema = chunkedDocument.ChunkedDocumentSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const document_specifications_1 = requireDocumentSpecifications();
	const chunks_1 = requireChunks();
	chunkedDocument.ChunkedDocumentSchema = document_specifications_1.DocumentChunkingSpecificationSchema.extend({
	    chunks: zod_1.z.array(chunks_1.ChunkContentSchema),
	});
	chunkedDocument.EmbeddedDocumentSchema = document_specifications_1.DocumentEmbeddingSpecificationSchema.extend({
	    chunks: zod_1.z.array(chunks_1.ChunkContentWithEmbeddingSchema),
	});
	
	return chunkedDocument;
}

var ingestionResults = {};

var hasRequiredIngestionResults;

function requireIngestionResults () {
	if (hasRequiredIngestionResults) return ingestionResults;
	hasRequiredIngestionResults = 1;
	Object.defineProperty(ingestionResults, "__esModule", { value: true });
	ingestionResults.IngestChunkedDocumentResultSchema = ingestionResults.IngestDocumentResultSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const document_specifications_1 = requireDocumentSpecifications();
	const search_providers_1 = requireSearchProviders();
	ingestionResults.IngestDocumentResultSchema = zod_1.z.object({
	    wasAlreadyIndexed: zod_1.z.boolean(),
	    chunksIndexed: zod_1.z.number(),
	    documentSpec: document_specifications_1.DocumentEmbeddingSpecificationSchema,
	    searchProvider: search_providers_1.SearchProviderNameSchema,
	});
	ingestionResults.IngestChunkedDocumentResultSchema = zod_1.z.object({
	    wasAlreadyIndexed: zod_1.z.boolean(),
	    chunksIndexed: zod_1.z.number(),
	    documentSpec: document_specifications_1.DocumentChunkingSpecificationSchema,
	    indexName: zod_1.z.string(),
	    searchProvider: search_providers_1.SearchProviderNameSchema,
	});
	
	return ingestionResults;
}

var requiredChunkMetadata = {};

var hasRequiredRequiredChunkMetadata;

function requireRequiredChunkMetadata () {
	if (hasRequiredRequiredChunkMetadata) return requiredChunkMetadata;
	hasRequiredRequiredChunkMetadata = 1;
	Object.defineProperty(requiredChunkMetadata, "__esModule", { value: true });
	requiredChunkMetadata.RequiredChunkMetadataSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const presets_1 = requirePresets();
	requiredChunkMetadata.RequiredChunkMetadataSchema = zod_1.z
	    .object({
	    fileId: zod_1.z.string(),
	    ocrPresetName: presets_1.OcrPresetNameSchema,
	    chunkingPresetName: presets_1.ChunkingPresetNameSchema,
	    chunkIndex: zod_1.z.number().int().nonnegative(),
	})
	    .passthrough();
	
	return requiredChunkMetadata;
}

var searchResults = {};

var hasRequiredSearchResults;

function requireSearchResults () {
	if (hasRequiredSearchResults) return searchResults;
	hasRequiredSearchResults = 1;
	Object.defineProperty(searchResults, "__esModule", { value: true });
	searchResults.SearchResultSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	searchResults.SearchResultSchema = zod_1.z.object({
	    chunkId: zod_1.z.string(),
	    textContent: zod_1.z.string(),
	    score: zod_1.z.number(),
	    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
	});
	
	return searchResults;
}

var hasRequiredDocumentRetrieval;

function requireDocumentRetrieval () {
	if (hasRequiredDocumentRetrieval) return DocumentRetrieval;
	hasRequiredDocumentRetrieval = 1;
	(function (exports) {
		var __createBinding = (DocumentRetrieval && DocumentRetrieval.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (DocumentRetrieval && DocumentRetrieval.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePresets(), exports);
		__exportStar(requireSearchIndexes(), exports);
		__exportStar(requireSearchOptions(), exports);
		__exportStar(requireQueryFusion(), exports);
		__exportStar(requireSearchProviders(), exports);
		__exportStar(requireDocumentSpecifications(), exports);
		__exportStar(requireChunks(), exports);
		__exportStar(requireChunkedDocument(), exports);
		__exportStar(requireIngestionResults(), exports);
		__exportStar(requireRequiredChunkMetadata(), exports);
		__exportStar(requireSearchResults(), exports);
		
	} (DocumentRetrieval));
	return DocumentRetrieval;
}

var isValidPdf = {};

var hasRequiredIsValidPdf;

function requireIsValidPdf () {
	if (hasRequiredIsValidPdf) return isValidPdf;
	hasRequiredIsValidPdf = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PDF_HEADER_SEARCH_WINDOW = void 0;
		exports.bytesAreValidPdf = bytesAreValidPdf;
		exports.blobIsValidPdf = blobIsValidPdf;
		exports.PDF_HEADER_SEARCH_WINDOW = 1024;
		const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d];
		function bytesAreValidPdf(bytes) {
		    const lastStart = Math.min(bytes.length, exports.PDF_HEADER_SEARCH_WINDOW) - PDF_SIGNATURE.length;
		    for (let start = 0; start <= lastStart; start++) {
		        let matched = true;
		        for (let offset = 0; offset < PDF_SIGNATURE.length; offset++) {
		            if (bytes[start + offset] !== PDF_SIGNATURE[offset]) {
		                matched = false;
		                break;
		            }
		        }
		        if (matched) {
		            return true;
		        }
		    }
		    return false;
		}
		async function blobIsValidPdf(blob) {
		    const header = await blob.slice(0, exports.PDF_HEADER_SEARCH_WINDOW).arrayBuffer();
		    return bytesAreValidPdf(new Uint8Array(header));
		}
		
	} (isValidPdf));
	return isValidPdf;
}

var pdfBody = {};

var hasRequiredPdfBody;

function requirePdfBody () {
	if (hasRequiredPdfBody) return pdfBody;
	hasRequiredPdfBody = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PdfBodyEncoding = exports.MAX_DECODED_PDF_BODY_BYTES = void 0;
		exports.inspectPdfBody = inspectPdfBody;
		const is_valid_pdf_1 = requireIsValidPdf();
		const GZIP_MAGIC = [0x1f, 0x8b];
		const GZIP_CONTENT_CODINGS = new Set(['gzip', 'x-gzip']);
		const MIN_GZIP_MEMBER_BYTES = 18;
		exports.MAX_DECODED_PDF_BODY_BYTES = 128 * 1024 * 1024;
		var PdfBodyEncoding;
		(function (PdfBodyEncoding) {
		    PdfBodyEncoding["Identity"] = "identity";
		    PdfBodyEncoding["Gzip"] = "gzip";
		})(PdfBodyEncoding || (exports.PdfBodyEncoding = PdfBodyEncoding = {}));
		async function inspectPdfBody(body, contentEncoding) {
		    const gzipMagic = hasGzipMagic(body);
		    if (gzipMagic || declaresGzip(contentEncoding)) {
		        try {
		            const inflated = await gunzip(body, exports.MAX_DECODED_PDF_BODY_BYTES);
		            return { isPdf: (0, is_valid_pdf_1.bytesAreValidPdf)(inflated), bytes: inflated, encoding: PdfBodyEncoding.Gzip };
		        }
		        catch {
		            if (gzipMagic) {
		                return { isPdf: false, bytes: body, encoding: PdfBodyEncoding.Identity };
		            }
		        }
		    }
		    return { isPdf: (0, is_valid_pdf_1.bytesAreValidPdf)(body), bytes: body, encoding: PdfBodyEncoding.Identity };
		}
		function hasGzipMagic(bytes) {
		    return bytes.length >= GZIP_MAGIC.length && bytes[0] === GZIP_MAGIC[0] && bytes[1] === GZIP_MAGIC[1];
		}
		function declaresGzip(contentEncoding) {
		    return (contentEncoding ?? '').split(',').some((coding) => GZIP_CONTENT_CODINGS.has(coding.trim().toLowerCase()));
		}
		async function gunzip(bytes, maxOutputBytes) {
		    if (bytes.length < MIN_GZIP_MEMBER_BYTES) {
		        throw new RangeError('gzip body is shorter than one gzip member');
		    }
		    const declaredSize = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(bytes.byteLength - 4, true);
		    if (declaredSize > maxOutputBytes) {
		        throw new RangeError(`gzip body declares ${declaredSize} bytes, past ${maxOutputBytes}`);
		    }
		    const reader = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip')).getReader();
		    const inflated = new Uint8Array(declaredSize);
		    let size = 0;
		    for (;;) {
		        const { done, value } = await reader.read();
		        if (done) {
		            break;
		        }
		        if (size + value.byteLength > declaredSize) {
		            await reader.cancel();
		            throw new RangeError(`gzip body inflates past its declared ${declaredSize} bytes`);
		        }
		        inflated.set(value, size);
		        size += value.byteLength;
		    }
		    return size === declaredSize ? inflated : inflated.subarray(0, size);
		}
		
	} (pdfBody));
	return pdfBody;
}

var citationPageElements_types = {};

var hasRequiredCitationPageElements_types;

function requireCitationPageElements_types () {
	if (hasRequiredCitationPageElements_types) return citationPageElements_types;
	hasRequiredCitationPageElements_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.CitationPageElementsPageSchema = exports.CitationPageElementSchema = exports.CitationTableRowSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const sigma_engine_shared_1 = requireDist$1();
		exports.CitationTableRowSchema = z.object({
		    rowIndex: z.number().int().nonnegative(),
		    text: z.string().nullable(),
		    boundingBox: sigma_engine_shared_1.boundingBoxSchema,
		});
		exports.CitationPageElementSchema = z.object({
		    elementIndex: z.number().int().nonnegative(),
		    elementType: sigma_engine_shared_1.PdfElementTypeSchema,
		    text: z.string().nullable(),
		    boundingBox: sigma_engine_shared_1.boundingBoxSchema,
		    tableRows: exports.CitationTableRowSchema.array().optional(),
		});
		exports.CitationPageElementsPageSchema = z.object({
		    pageNumber: z.number().int().positive(),
		    elements: exports.CitationPageElementSchema.array(),
		});
		
	} (citationPageElements_types));
	return citationPageElements_types;
}

var Templating = {};

var templating = {};

var hasRequiredTemplating$1;

function requireTemplating$1 () {
	if (hasRequiredTemplating$1) return templating;
	hasRequiredTemplating$1 = 1;
	Object.defineProperty(templating, "__esModule", { value: true });
	templating.applySubstitutionsToTemplate = applySubstitutionsToTemplate;
	const sigma_engine_shared_1 = requireDist$1();
	function applySubstitutionsToTemplate(templateText, substitutions) {
	    return substitutions.reduce((text, template) => {
	        const regex = new RegExp(`{{\\s*${template.name}\\s*}}`, 'g');
	        return text.replace(regex, sigma_engine_shared_1.Utils.resolveValueFromThunk(template.value));
	    }, templateText);
	}
	
	return templating;
}

var hasRequiredTemplating;

function requireTemplating () {
	if (hasRequiredTemplating) return Templating;
	hasRequiredTemplating = 1;
	(function (exports) {
		var __createBinding = (Templating && Templating.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Templating && Templating.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireTemplating$1(), exports);
		
	} (Templating));
	return Templating;
}

var Utils$2 = {};

var deepFreeze = {};

var hasRequiredDeepFreeze;

function requireDeepFreeze () {
	if (hasRequiredDeepFreeze) return deepFreeze;
	hasRequiredDeepFreeze = 1;
	Object.defineProperty(deepFreeze, "__esModule", { value: true });
	deepFreeze.deepFreeze = deepFreeze$1;
	function deepFreeze$1(value) {
	    freezeRecursively(value, new WeakSet());
	    return value;
	}
	function freezeRecursively(value, seen) {
	    if (value === null || typeof value !== 'object' || seen.has(value)) {
	        return;
	    }
	    seen.add(value);
	    for (const nested of Object.values(value)) {
	        freezeRecursively(nested, seen);
	    }
	    Object.freeze(value);
	}
	
	return deepFreeze;
}

var utcDay = {};

var hasRequiredUtcDay;

function requireUtcDay () {
	if (hasRequiredUtcDay) return utcDay;
	hasRequiredUtcDay = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DAY_MS = void 0;
		exports.msToUtcDay = msToUtcDay;
		exports.utcDayStartMs = utcDayStartMs;
		exports.utcMidnightMs = utcMidnightMs;
		exports.buildUtcDayList = buildUtcDayList;
		exports.windowStartMs = windowStartMs;
		exports.DAY_MS = 24 * 60 * 60 * 1000;
		function msToUtcDay(ms) {
		    return new Date(ms).toISOString().slice(0, 10);
		}
		function utcDayStartMs(day) {
		    return Date.parse(`${day}T00:00:00.000Z`);
		}
		function utcMidnightMs(ms) {
		    const d = new Date(ms);
		    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
		}
		function buildUtcDayList(nowMs, lookbackDays) {
		    const todayStart = utcMidnightMs(nowMs);
		    const days = [];
		    for (let i = lookbackDays - 1; i >= 0; i -= 1) {
		        days.push(msToUtcDay(todayStart - i * exports.DAY_MS));
		    }
		    return days;
		}
		function windowStartMs(nowMs, lookbackDays) {
		    return utcMidnightMs(nowMs) - (lookbackDays - 1) * exports.DAY_MS;
		}
		
	} (utcDay));
	return utcDay;
}

var businessDays = {};

var hasRequiredBusinessDays;

function requireBusinessDays () {
	if (hasRequiredBusinessDays) return businessDays;
	hasRequiredBusinessDays = 1;
	Object.defineProperty(businessDays, "__esModule", { value: true });
	businessDays.isWeekend = isWeekend;
	businessDays.addBusinessDays = addBusinessDays;
	businessDays.businessDaysBetween = businessDaysBetween;
	businessDays.clipAndMergeIntervals = clipAndMergeIntervals;
	businessDays.netBusinessDays = netBusinessDays;
	const SUNDAY = 0;
	const SATURDAY = 6;
	const formattersByTimezone = new Map();
	function formatterFor(timezone) {
	    const cached = formattersByTimezone.get(timezone);
	    if (cached !== undefined) {
	        return cached;
	    }
	    const formatter = new Intl.DateTimeFormat('en-US', {
	        timeZone: timezone,
	        hourCycle: 'h23',
	        year: 'numeric',
	        month: '2-digit',
	        day: '2-digit',
	        hour: '2-digit',
	        minute: '2-digit',
	        second: '2-digit',
	    });
	    formattersByTimezone.set(timezone, formatter);
	    return formatter;
	}
	function toLocal(ms, timezone) {
	    const parts = new Map();
	    for (const part of formatterFor(timezone).formatToParts(new Date(ms))) {
	        if (part.type !== 'literal') {
	            parts.set(part.type, Number(part.value));
	        }
	    }
	    return {
	        year: parts.get('year') ?? 0,
	        month: parts.get('month') ?? 1,
	        day: parts.get('day') ?? 1,
	        hour: parts.get('hour') ?? 0,
	        minute: parts.get('minute') ?? 0,
	        second: parts.get('second') ?? 0,
	        millisecond: ((ms % 1000) + 1000) % 1000,
	    };
	}
	function fromLocal(local, timezone) {
	    const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second, local.millisecond);
	    const firstGuess = asUtc - offsetMs(asUtc, timezone);
	    const secondOffset = offsetMs(firstGuess, timezone);
	    return asUtc - secondOffset;
	}
	function offsetMs(ms, timezone) {
	    const local = toLocal(ms, timezone);
	    const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
	    return asUtc - (ms - local.millisecond);
	}
	function weekdayOf(local) {
	    return new Date(Date.UTC(local.year, local.month - 1, local.day)).getUTCDay();
	}
	function isWeekendDate(local) {
	    const weekday = weekdayOf(local);
	    return weekday === SUNDAY || weekday === SATURDAY;
	}
	function plusDays(local, days) {
	    const stepped = new Date(Date.UTC(local.year, local.month - 1, local.day + days));
	    return {
	        ...local,
	        year: stepped.getUTCFullYear(),
	        month: stepped.getUTCMonth() + 1,
	        day: stepped.getUTCDate(),
	    };
	}
	function startOfDay(local, timezone) {
	    return fromLocal({ ...local, hour: 0, minute: 0, second: 0, millisecond: 0 }, timezone);
	}
	function isWeekend(date, timezone) {
	    return isWeekendDate(toLocal(date.getTime(), timezone));
	}
	function addBusinessDays(startTimestamp, businessDays, timezone) {
	    if (businessDays <= 0) {
	        return startTimestamp;
	    }
	    let current = toLocal(startTimestamp, timezone);
	    let remaining = businessDays;
	    while (remaining > 0) {
	        current = plusDays(current, 1);
	        if (!isWeekendDate(current)) {
	            remaining -= 1;
	        }
	    }
	    return fromLocal(current, timezone);
	}
	function businessDaysBetween(startMs, endMs, timezone) {
	    if (endMs <= startMs) {
	        return 0;
	    }
	    let total = 0;
	    let day = toLocal(startMs, timezone);
	    let dayStart = startOfDay(day, timezone);
	    while (dayStart < endMs) {
	        const nextDay = plusDays(day, 1);
	        const dayEnd = startOfDay(nextDay, timezone);
	        if (!isWeekendDate(day)) {
	            const overlap = Math.min(endMs, dayEnd) - Math.max(startMs, dayStart);
	            total += overlap / (dayEnd - dayStart);
	        }
	        day = nextDay;
	        dayStart = dayEnd;
	    }
	    return total;
	}
	function clipAndMergeIntervals(intervals, startMs, endMs) {
	    const clipped = intervals
	        .map((interval) => ({
	        startMs: Math.max(interval.startMs, startMs),
	        endMs: Math.min(interval.endMs ?? endMs, endMs),
	    }))
	        .filter((interval) => interval.endMs > interval.startMs)
	        .sort((left, right) => left.startMs - right.startMs);
	    const merged = [];
	    for (const interval of clipped) {
	        const last = merged[merged.length - 1];
	        if (last !== undefined && interval.startMs <= last.endMs) {
	            last.endMs = Math.max(last.endMs, interval.endMs);
	        }
	        else {
	            merged.push({ ...interval });
	        }
	    }
	    return merged;
	}
	function netBusinessDays(startMs, endMs, pausedIntervals, timezone) {
	    const gross = businessDaysBetween(startMs, endMs, timezone);
	    const paused = clipAndMergeIntervals(pausedIntervals, startMs, endMs).reduce((sum, interval) => sum + businessDaysBetween(interval.startMs, interval.endMs, timezone), 0);
	    return Math.max(0, gross - paused);
	}
	
	return businessDays;
}

var restrictedDiscoverableDecorator = {};

var hasRequiredRestrictedDiscoverableDecorator;

function requireRestrictedDiscoverableDecorator () {
	if (hasRequiredRestrictedDiscoverableDecorator) return restrictedDiscoverableDecorator;
	hasRequiredRestrictedDiscoverableDecorator = 1;
	Object.defineProperty(restrictedDiscoverableDecorator, "__esModule", { value: true });
	
	return restrictedDiscoverableDecorator;
}

var zodRequiredUnknown = {};

var hasRequiredZodRequiredUnknown;

function requireZodRequiredUnknown () {
	if (hasRequiredZodRequiredUnknown) return zodRequiredUnknown;
	hasRequiredZodRequiredUnknown = 1;
	Object.defineProperty(zodRequiredUnknown, "__esModule", { value: true });
	zodRequiredUnknown.zodRequiredUnknown = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const zodRequiredUnknown$1 = () => zod_1.z.custom((v) => v !== undefined && v !== null, {
	    message: 'is required and may not be null',
	});
	zodRequiredUnknown.zodRequiredUnknown = zodRequiredUnknown$1;
	
	return zodRequiredUnknown;
}

var zodJsonSchema = {};

var hasRequiredZodJsonSchema;

function requireZodJsonSchema () {
	if (hasRequiredZodJsonSchema) return zodJsonSchema;
	hasRequiredZodJsonSchema = 1;
	Object.defineProperty(zodJsonSchema, "__esModule", { value: true });
	zodJsonSchema.ZodJsonSchema = zodJsonSchema.ZodJsonSchemaTarget = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var ZodJsonSchemaTarget;
	(function (ZodJsonSchemaTarget) {
	    ZodJsonSchemaTarget["JsonSchema7"] = "json_schema_7";
	    ZodJsonSchemaTarget["OpenAi"] = "open_ai";
	    ZodJsonSchemaTarget["OpenAiSdkHelper"] = "open_ai_sdk_helper";
	})(ZodJsonSchemaTarget || (zodJsonSchema.ZodJsonSchemaTarget = ZodJsonSchemaTarget = {}));
	const OPEN_AI_ANY_TYPE = 'OpenAiAnyType';
	const SUBSCHEMA_KEYWORDS = ['additionalProperties', 'items', 'not'];
	const SUBSCHEMA_LIST_KEYWORDS = ['anyOf', 'allOf', 'prefixItems', 'items'];
	const SUBSCHEMA_MAP_KEYWORDS = ['properties', '$defs', 'definitions'];
	const TYPING_KEYWORDS = ['type', 'anyOf', '$ref', 'enum', 'const'];
	const CHECK_NAME_FORMATS = new Set(['starts_with', 'ends_with', 'includes', 'regex']);
	class ZodJsonSchema {
	    static fromSchema(schema, options = {}) {
	        const target = options.target ?? ZodJsonSchemaTarget.JsonSchema7;
	        const openAi = target !== ZodJsonSchemaTarget.JsonSchema7;
	        const document = zod_1.z.toJSONSchema(schema, {
	            target: openAi ? 'draft-2020-12' : 'draft-07',
	            io: 'input',
	            reused: 'inline',
	            unrepresentable: (ctx) => ZodJsonSchema.describeUnrepresentable(ctx),
	            override: (ctx) => {
	                ZodJsonSchema.closeStrippedObject(ctx);
	                if (openAi) {
	                    ZodJsonSchema.requireOptionalAsNullable(ctx, target === ZodJsonSchemaTarget.OpenAiSdkHelper);
	                }
	                else {
	                    ZodJsonSchema.requireKeysThatRejectUndefined(ctx);
	                }
	            },
	        });
	        ZodJsonSchema.normalize(document);
	        if (openAi) {
	            ZodJsonSchema.restrictToOpenAiSubset(document);
	        }
	        return { ...document };
	    }
	    static parseFromOpenAi(schema, value) {
	        return schema.parse(ZodJsonSchema.dropNullsMeaningOmitted(schema, value));
	    }
	    static safeParseFromOpenAi(schema, value) {
	        return schema.safeParse(ZodJsonSchema.dropNullsMeaningOmitted(schema, value));
	    }
	    static describeUnrepresentable(ctx) {
	        switch (ctx.zodSchema._zod.def.type) {
	            case 'date':
	                return { type: 'string', format: 'date-time' };
	            case 'bigint':
	                return { type: 'integer', format: 'int64' };
	            case 'undefined':
	            case 'void':
	            case 'nan':
	            case 'symbol':
	            case 'function':
	                return { not: {} };
	            case 'set':
	                return { type: 'array', uniqueItems: true };
	            case 'map':
	                return { type: 'array' };
	            case 'custom':
	                return ZodJsonSchema.acceptsOnlyFunctions(ctx.zodSchema) ? { not: {} } : 'any';
	            default:
	                return 'any';
	        }
	    }
	    static acceptsOnlyFunctions(schema) {
	        return (zod_1.z.safeParse(schema, () => undefined).success &&
	            [null, {}, [], '', 0, false].every((value) => !zod_1.z.safeParse(schema, value).success));
	    }
	    static requireKeysThatRejectUndefined(ctx) {
	        const def = ctx.zodSchema._zod.def;
	        const properties = ctx.jsonSchema.properties;
	        if (def.type !== 'object' || properties === undefined) {
	            return;
	        }
	        const required = Object.entries(def.shape)
	            .filter(([key, field]) => key in properties && !ZodJsonSchema.acceptsUndefined(field))
	            .map(([key]) => key);
	        if (required.length > 0) {
	            ctx.jsonSchema.required = required;
	        }
	        else {
	            delete ctx.jsonSchema.required;
	        }
	    }
	    static dropNullsMeaningOmitted(schema, value) {
	        if (schema instanceof zod_1.z.core.$ZodObject) {
	            if (typeof value !== 'object' || value === null || Array.isArray(value)) {
	                return value;
	            }
	            const cleaned = { ...value };
	            for (const [key, field] of Object.entries(schema._zod.def.shape)) {
	                if (cleaned[key] === null && !zod_1.z.safeParse(field, null).success && ZodJsonSchema.acceptsUndefined(field)) {
	                    delete cleaned[key];
	                }
	                else if (key in cleaned) {
	                    cleaned[key] = ZodJsonSchema.dropNullsMeaningOmitted(field, cleaned[key]);
	                }
	            }
	            return cleaned;
	        }
	        if (schema instanceof zod_1.z.core.$ZodArray) {
	            return Array.isArray(value) ?
	                value.map((item) => ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.element, item))
	                : value;
	        }
	        if (schema instanceof zod_1.z.core.$ZodTuple) {
	            const { items, rest } = schema._zod.def;
	            return Array.isArray(value) ?
	                value.map((item, i) => ZodJsonSchema.dropNullsMeaningOmitted(items[i] ?? rest ?? zod_1.z.unknown(), item))
	                : value;
	        }
	        if (schema instanceof zod_1.z.core.$ZodRecord) {
	            if (typeof value !== 'object' || value === null || Array.isArray(value)) {
	                return value;
	            }
	            return Object.fromEntries(Object.entries(value).map(([key, item]) => [
	                key,
	                ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.valueType, item),
	            ]));
	        }
	        if (schema instanceof zod_1.z.core.$ZodUnion) {
	            for (const option of schema._zod.def.options) {
	                const cleaned = ZodJsonSchema.dropNullsMeaningOmitted(option, value);
	                if (zod_1.z.safeParse(option, cleaned).success) {
	                    return cleaned;
	                }
	            }
	            return value;
	        }
	        if (schema instanceof zod_1.z.core.$ZodIntersection) {
	            return ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.right, ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.left, value));
	        }
	        if (schema instanceof zod_1.z.core.$ZodPipe) {
	            const { in: input, out } = schema._zod.def;
	            return ZodJsonSchema.dropNullsMeaningOmitted(input instanceof zod_1.z.core.$ZodTransform ? out : input, value);
	        }
	        if (schema instanceof zod_1.z.core.$ZodLazy) {
	            return ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.innerType, value);
	        }
	        if (schema instanceof zod_1.z.core.$ZodOptional ||
	            schema instanceof zod_1.z.core.$ZodNullable ||
	            schema instanceof zod_1.z.core.$ZodDefault ||
	            schema instanceof zod_1.z.core.$ZodPrefault ||
	            schema instanceof zod_1.z.core.$ZodNonOptional ||
	            schema instanceof zod_1.z.core.$ZodReadonly ||
	            schema instanceof zod_1.z.core.$ZodCatch) {
	            return ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.innerType, value);
	        }
	        return value;
	    }
	    static acceptsUndefined(field) {
	        try {
	            return zod_1.z.safeParse(field, undefined).success;
	        }
	        catch {
	            return false;
	        }
	    }
	    static closeStrippedObject(ctx) {
	        const def = ctx.zodSchema._zod.def;
	        if (def.type === 'object' && def.catchall === undefined && ctx.jsonSchema.additionalProperties === undefined) {
	            ctx.jsonSchema.additionalProperties = false;
	        }
	    }
	    static requireOptionalAsNullable(ctx, keepDefaultedNonNull) {
	        const def = ctx.zodSchema._zod.def;
	        const properties = ctx.jsonSchema.properties;
	        if (def.type !== 'object' || properties === undefined) {
	            return;
	        }
	        for (const [key, field] of Object.entries(def.shape)) {
	            const property = properties[key];
	            if (typeof property !== 'object') {
	                continue;
	            }
	            if (ZodJsonSchema.matchesNothing(property)) {
	                delete properties[key];
	                continue;
	            }
	            if (!ZodJsonSchema.acceptsUndefined(field)) {
	                continue;
	            }
	            if (keepDefaultedNonNull && (field._zod.def.type === 'default' || field._zod.def.type === 'prefault')) {
	                continue;
	            }
	            const valueSchema = field._zod.def.type === 'optional' ? field._zod.def.innerType : field;
	            if (!zod_1.z.safeParse(valueSchema, null).success) {
	                properties[key] = { anyOf: [property, { type: 'null' }] };
	            }
	        }
	        ctx.jsonSchema.required = Object.keys(properties);
	    }
	    static normalize(document) {
	        ZodJsonSchema.forEachNode(document, (node) => {
	            if (Array.isArray(node.oneOf)) {
	                node.anyOf = node.oneOf;
	                delete node.oneOf;
	            }
	            if (ZodJsonSchema.isPlainStringKeys(node.propertyNames)) {
	                delete node.propertyNames;
	            }
	            delete node.readOnly;
	            if (ZodJsonSchema.isNode(node.additionalProperties) && Object.keys(node.additionalProperties).length === 0) {
	                node.additionalProperties = true;
	            }
	            if (typeof node.format === 'string' && CHECK_NAME_FORMATS.has(node.format) && 'pattern' in node) {
	                delete node.format;
	            }
	        });
	    }
	    static restrictToOpenAiSubset(document) {
	        const anyTypeRef = `#/$defs/${OPEN_AI_ANY_TYPE}`;
	        let usesAnyType = false;
	        const negated = new Set();
	        ZodJsonSchema.forEachNode(document, (node) => {
	            if (ZodJsonSchema.isNode(node.not)) {
	                negated.add(node.not);
	            }
	            if (Array.isArray(node.anyOf)) {
	                node.anyOf = node.anyOf.filter((branch) => !ZodJsonSchema.isNode(branch) || !ZodJsonSchema.matchesNothing(branch));
	            }
	            if (!negated.has(node) &&
	                !ZodJsonSchema.matchesNothing(node) &&
	                !TYPING_KEYWORDS.some((keyword) => keyword in node)) {
	                node.$ref = anyTypeRef;
	                usesAnyType = true;
	            }
	        });
	        if (usesAnyType) {
	            const defs = ZodJsonSchema.isNode(document.$defs) ? document.$defs : {};
	            defs[OPEN_AI_ANY_TYPE] = {
	                type: ['string', 'number', 'integer', 'boolean', 'array', 'null'],
	                items: { $ref: anyTypeRef },
	            };
	            document.$defs = defs;
	        }
	    }
	    static forEachNode(node, visit) {
	        visit(node);
	        for (const keyword of SUBSCHEMA_KEYWORDS) {
	            const child = node[keyword];
	            if (ZodJsonSchema.isNode(child)) {
	                ZodJsonSchema.forEachNode(child, visit);
	            }
	        }
	        for (const keyword of SUBSCHEMA_LIST_KEYWORDS) {
	            const children = node[keyword];
	            if (Array.isArray(children)) {
	                children
	                    .filter((child) => ZodJsonSchema.isNode(child))
	                    .forEach((child) => ZodJsonSchema.forEachNode(child, visit));
	            }
	        }
	        for (const keyword of SUBSCHEMA_MAP_KEYWORDS) {
	            const children = node[keyword];
	            if (ZodJsonSchema.isNode(children)) {
	                Object.values(children)
	                    .filter((child) => ZodJsonSchema.isNode(child))
	                    .forEach((child) => ZodJsonSchema.forEachNode(child, visit));
	            }
	        }
	    }
	    static isNode(value) {
	        return typeof value === 'object' && value !== null && !Array.isArray(value);
	    }
	    static isPlainStringKeys(value) {
	        return ZodJsonSchema.isNode(value) && Object.keys(value).length === 1 && value.type === 'string';
	    }
	    static matchesNothing(node) {
	        const entries = Object.entries(node);
	        return (entries.length === 1 &&
	            entries[0][0] === 'not' &&
	            ZodJsonSchema.isNode(entries[0][1]) &&
	            Object.keys(entries[0][1]).length === 0);
	    }
	}
	zodJsonSchema.ZodJsonSchema = ZodJsonSchema;
	
	return zodJsonSchema;
}

var failureChain = {};

var hasRequiredFailureChain;

function requireFailureChain () {
	if (hasRequiredFailureChain) return failureChain;
	hasRequiredFailureChain = 1;
	Object.defineProperty(failureChain, "__esModule", { value: true });
	failureChain.FailureChain = void 0;
	class FailureChain {
	    static { this.MAX_DEPTH = 32; }
	    static innermost(error) {
	        if (!(error instanceof Error)) {
	            return { message: String(error) };
	        }
	        let message = error.message;
	        let type;
	        let current = error;
	        const seen = new Set();
	        for (let depth = 0; depth < FailureChain.MAX_DEPTH && current instanceof Error && !seen.has(current); depth++) {
	            seen.add(current);
	            if (current.message.length > 0) {
	                message = current.message;
	            }
	            if ('type' in current && typeof current.type === 'string' && current.type.length > 0) {
	                type = current.type;
	            }
	            current = current.cause;
	        }
	        return type === undefined ? { message } : { type, message };
	    }
	}
	failureChain.FailureChain = FailureChain;
	
	return failureChain;
}

var hasRequiredUtils;

function requireUtils () {
	if (hasRequiredUtils) return Utils$2;
	hasRequiredUtils = 1;
	(function (exports) {
		var __createBinding = (Utils$2 && Utils$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Utils$2 && Utils$2.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireBase64$1(), exports);
		__exportStar(requireDeepFreeze(), exports);
		__exportStar(requireUtcDay(), exports);
		__exportStar(requireBusinessDays(), exports);
		__exportStar(requireRestrictedDiscoverableDecorator(), exports);
		__exportStar(requireZodRequiredUnknown(), exports);
		__exportStar(requireZodJsonSchema(), exports);
		__exportStar(requireJsonValue(), exports);
		__exportStar(requireFailureChain(), exports);
		
	} (Utils$2));
	return Utils$2;
}

var Wizard = {};

var admin = {};

var hasRequiredAdmin;

function requireAdmin () {
	if (hasRequiredAdmin) return admin;
	hasRequiredAdmin = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.adminPlansSchema = exports.adminPlanArrSchema = exports.adminPlanSchema = exports.adminFragmentSchema = exports.adminStepSchema = exports.priorityBucketSchema = exports.PriorityBucket = exports.PlanPruneReason = exports.spawnPurposeSchema = exports.SpawnPurpose = exports.PlanFragmentStatus = exports.OrchestratorStepStatus = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const plan_status_1 = requirePlanStatus();
		var OrchestratorStepStatus;
		(function (OrchestratorStepStatus) {
		    OrchestratorStepStatus["Created"] = "CREATED";
		    OrchestratorStepStatus["Finalizing"] = "FINALIZING";
		    OrchestratorStepStatus["Completed"] = "COMPLETED";
		    OrchestratorStepStatus["Cancelled"] = "CANCELLED";
		})(OrchestratorStepStatus || (exports.OrchestratorStepStatus = OrchestratorStepStatus = {}));
		var PlanFragmentStatus;
		(function (PlanFragmentStatus) {
		    PlanFragmentStatus["Pending"] = "pending";
		    PlanFragmentStatus["Complete"] = "complete";
		    PlanFragmentStatus["Failed"] = "failed";
		    PlanFragmentStatus["Cancelled"] = "cancelled";
		})(PlanFragmentStatus || (exports.PlanFragmentStatus = PlanFragmentStatus = {}));
		var SpawnPurpose;
		(function (SpawnPurpose) {
		    SpawnPurpose["FirstReview"] = "first_review";
		    SpawnPurpose["DataScience"] = "data_science";
		    SpawnPurpose["ReReview"] = "re_review";
		    SpawnPurpose["UxResearch"] = "ux_research";
		    SpawnPurpose["PackageDataAdjudication"] = "package_data_adjudication";
		})(SpawnPurpose || (exports.SpawnPurpose = SpawnPurpose = {}));
		exports.spawnPurposeSchema = zod_1.z.nativeEnum(SpawnPurpose);
		var PlanPruneReason;
		(function (PlanPruneReason) {
		    PlanPruneReason["PrunedStale"] = "pruned_stale";
		    PlanPruneReason["PrunedPublished"] = "pruned_published";
		})(PlanPruneReason || (exports.PlanPruneReason = PlanPruneReason = {}));
		var PriorityBucket;
		(function (PriorityBucket) {
		    PriorityBucket["Critical"] = "critical";
		    PriorityBucket["Highest"] = "highest";
		    PriorityBucket["Higher"] = "higher";
		    PriorityBucket["Normal"] = "normal";
		    PriorityBucket["Lower"] = "lower";
		})(PriorityBucket || (exports.PriorityBucket = PriorityBucket = {}));
		exports.priorityBucketSchema = zod_1.z.nativeEnum(PriorityBucket);
		exports.adminStepSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    type: zod_1.z.string(),
		    displayName: zod_1.z.string().nullable(),
		    status: zod_1.z.nativeEnum(OrchestratorStepStatus),
		    isClaimable: zod_1.z.boolean(),
		    createdAt: zod_1.z.number(),
		    deadlineAt: zod_1.z.number(),
		    priorityBucket: exports.priorityBucketSchema.nullable(),
		});
		exports.adminFragmentSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    name: zod_1.z.string(),
		    status: zod_1.z.nativeEnum(PlanFragmentStatus),
		    claim: zod_1.z
		        .object({
		        userId: zod_1.z.string(),
		        expiresAt: zod_1.z.number(),
		        stepId: zod_1.z.string().nullable(),
		    })
		        .nullable(),
		    displayMetadata: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		    steps: exports.adminStepSchema.array(),
		    completedByUserId: zod_1.z.string().nullable(),
		    completedByUserName: zod_1.z.string().nullable(),
		});
		exports.adminPlanSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    fragments: zod_1.z.record(zod_1.z.string(), exports.adminFragmentSchema),
		    status: zod_1.z.nativeEnum(plan_status_1.PlanStatus),
		    createdAt: zod_1.z.number(),
		    deadlineAt: zod_1.z.number(),
		    completedAt: zod_1.z.number().nullable(),
		    generator: zod_1.z.string(),
		    packageId: zod_1.z.string().nullable(),
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    executionMode: zod_1.z.string().nullable(),
		    assignedToUserId: zod_1.z.string().nullable(),
		    assignedToUserName: zod_1.z.string().nullable(),
		    assignedToOrgId: zod_1.z.string().nullable(),
		    assignedToOrgName: zod_1.z.string().nullable(),
		    priority: zod_1.z.number().nullable(),
		});
		exports.adminPlanArrSchema = exports.adminPlanSchema.omit({ fragments: true }).extend({
		    fragments: exports.adminFragmentSchema.array(),
		});
		exports.adminPlansSchema = zod_1.z.object({
		    plans: zod_1.z.record(zod_1.z.string(), exports.adminPlanSchema),
		});
		
	} (admin));
	return admin;
}

var adminPlanFilterSchema = {};

var operatorTimeLedger = {};

var hasRequiredOperatorTimeLedger;

function requireOperatorTimeLedger () {
	if (hasRequiredOperatorTimeLedger) return operatorTimeLedger;
	hasRequiredOperatorTimeLedger = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.operatorTimeLedgerSchema = exports.SegmentEndReasons = exports.OPERATOR_TIME_PIN_BUCKETS = exports.MIN_BUCKET_PASSES = exports.MIN_OPERATOR_PASSES = exports.OPERATOR_TIME_IQR_MULTIPLIER = exports.MAX_REVIEW_DURATION_HOURS = exports.MIN_SECONDS_PER_PIN = exports.EVAL_EXECUTION_MODE = exports.DEFAULT_TIME_LEDGER_LOOKBACK_DAYS = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.DEFAULT_TIME_LEDGER_LOOKBACK_DAYS = 90;
		exports.EVAL_EXECUTION_MODE = 'operatorEval';
		exports.MIN_SECONDS_PER_PIN = 15;
		exports.MAX_REVIEW_DURATION_HOURS = 4;
		exports.OPERATOR_TIME_IQR_MULTIPLIER = 1.5;
		exports.MIN_OPERATOR_PASSES = 8;
		exports.MIN_BUCKET_PASSES = 20;
		exports.OPERATOR_TIME_PIN_BUCKETS = [
		    { key: 'b1', label: '1–3', minPins: 1, maxPins: 3 },
		    { key: 'b2', label: '4–8', minPins: 4, maxPins: 8 },
		    { key: 'b3', label: '9–16', minPins: 9, maxPins: 16 },
		    { key: 'b4', label: '17–32', minPins: 17, maxPins: 32 },
		    { key: 'b5', label: '33–64', minPins: 33, maxPins: 64 },
		    { key: 'b6', label: '65–300', minPins: 65, maxPins: 300 },
		];
		exports.SegmentEndReasons = ['submitted', 'released', 'expired', 'superseded'];
		const operatorTimeSegmentSchema = zod_1.z.object({
		    operatorId: zod_1.z.string(),
		    operatorEmail: zod_1.z.string().nullable(),
		    operatorName: zod_1.z.string().nullable(),
		    username: zod_1.z.string().nullable(),
		    planId: zod_1.z.string(),
		    reviewId: zod_1.z.string().nullable(),
		    generatorName: zod_1.z.string(),
		    executionMode: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().nullable(),
		    packageName: zod_1.z.string().nullable(),
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    packagePinCountInitialEstimate: zod_1.z.number().nullable(),
		    publishedPackagePinCount: zod_1.z.number().nullable(),
		    sigmaModelType: zod_1.z.string().nullable(),
		    stepId: zod_1.z.string(),
		    stepType: zod_1.z.string(),
		    startedAtUtc: zod_1.z.string(),
		    endedAtUtc: zod_1.z.string(),
		    heldSeconds: zod_1.z.number(),
		    engagedSeconds: zod_1.z.number().nullable(),
		    endReason: zod_1.z.enum(exports.SegmentEndReasons),
		    passNumber: zod_1.z.number(),
		});
		const operatorStepTimingSchema = zod_1.z.object({
		    operatorId: zod_1.z.string(),
		    operatorEmail: zod_1.z.string().nullable(),
		    operatorName: zod_1.z.string().nullable(),
		    username: zod_1.z.string().nullable(),
		    planId: zod_1.z.string(),
		    reviewId: zod_1.z.string().nullable(),
		    generatorName: zod_1.z.string(),
		    executionMode: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().nullable(),
		    packageName: zod_1.z.string().nullable(),
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    packagePinCountInitialEstimate: zod_1.z.number().nullable(),
		    publishedPackagePinCount: zod_1.z.number().nullable(),
		    sigmaModelType: zod_1.z.string().nullable(),
		    stepId: zod_1.z.string(),
		    stepType: zod_1.z.string(),
		    startedAtUtc: zod_1.z.string(),
		    endedAtUtc: zod_1.z.string(),
		    heldSeconds: zod_1.z.number(),
		    engagedSeconds: zod_1.z.number().nullable(),
		    idleSeconds: zod_1.z.number().nullable(),
		    engagementRatio: zod_1.z.number().nullable(),
		    viewingCount: zod_1.z.number(),
		    status: zod_1.z.string(),
		});
		const reviewRollupEntrySchema = zod_1.z.object({
		    reviewId: zod_1.z.string(),
		    planId: zod_1.z.string(),
		    generatorName: zod_1.z.string(),
		    executionMode: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().nullable(),
		    packageName: zod_1.z.string().nullable(),
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    packagePinCountInitialEstimate: zod_1.z.number().nullable(),
		    publishedPackagePinCount: zod_1.z.number().nullable(),
		    sigmaModelType: zod_1.z.string().nullable(),
		    completedAt: zod_1.z.string().nullable(),
		    operatorIds: zod_1.z.array(zod_1.z.string()),
		    startedAtUtc: zod_1.z.string(),
		    endedAtUtc: zod_1.z.string(),
		    heldSeconds: zod_1.z.number(),
		    engagedSeconds: zod_1.z.number().nullable(),
		    stepCount: zod_1.z.number(),
		    viewingCount: zod_1.z.number(),
		});
		const operatorDailySummarySchema = zod_1.z.object({
		    operatorId: zod_1.z.string(),
		    operatorName: zod_1.z.string().nullable(),
		    username: zod_1.z.string().nullable(),
		    date: zod_1.z.string(),
		    executionMode: zod_1.z.string().nullable(),
		    estimatedEngagedHours: zod_1.z.number(),
		    measuredEngagedHours: zod_1.z.number().nullable(),
		    presenceHours: zod_1.z.number(),
		    reviewsCompleted: zod_1.z.number(),
		    totalPins: zod_1.z.number(),
		});
		const operatorWeeklySummarySchema = zod_1.z.object({
		    operatorId: zod_1.z.string(),
		    operatorName: zod_1.z.string().nullable(),
		    username: zod_1.z.string().nullable(),
		    weekKey: zod_1.z.string(),
		    weekStart: zod_1.z.string(),
		    weekEnd: zod_1.z.string(),
		    executionMode: zod_1.z.string().nullable(),
		    estimatedEngagedHours: zod_1.z.number(),
		    measuredEngagedHours: zod_1.z.number().nullable(),
		    presenceHours: zod_1.z.number(),
		    reviewsCompleted: zod_1.z.number(),
		    totalPins: zod_1.z.number(),
		});
		exports.operatorTimeLedgerSchema = zod_1.z.object({
		    generatedAt: zod_1.z.string(),
		    from: zod_1.z.string(),
		    to: zod_1.z.string(),
		    segments: zod_1.z.array(operatorTimeSegmentSchema),
		    stepTimings: zod_1.z.array(operatorStepTimingSchema),
		    reviewRollup: zod_1.z.array(reviewRollupEntrySchema),
		    dailyOperatorSummary: zod_1.z.array(operatorDailySummarySchema),
		    weeklyOperatorSummary: zod_1.z.array(operatorWeeklySummarySchema),
		});
		
	} (operatorTimeLedger));
	return operatorTimeLedger;
}

var hasRequiredAdminPlanFilterSchema;

function requireAdminPlanFilterSchema () {
	if (hasRequiredAdminPlanFilterSchema) return adminPlanFilterSchema;
	hasRequiredAdminPlanFilterSchema = 1;
	Object.defineProperty(adminPlanFilterSchema, "__esModule", { value: true });
	adminPlanFilterSchema.adminPlanCountFilterSchema = adminPlanFilterSchema.adminPlanFilterSchema = void 0;
	const QueryBuilder_1 = requireQueryBuilder();
	const operator_time_ledger_1 = requireOperatorTimeLedger();
	const STRING_ALLOWED_OPS = [
	    'contains',
	    'doesNotContain',
	    'equals',
	    'doesNotEqual',
	    'isAnyOf',
	    'startsWith',
	    'endsWith',
	    'isEmpty',
	    'isNotEmpty',
	];
	const TIMESTAMP_ALLOWED_OPS = ['greaterThan', 'lessThan'];
	const TRAINING_PLAN_EXECUTION_MODE = operator_time_ledger_1.EVAL_EXECUTION_MODE;
	const NEGATIVE_STRING_OPERATORS = ['doesNotContain', 'doesNotEqual', 'not'];
	const sharedPlanFields = {
	    isTrainingPlan: {
	        description: `True iff the plan's executionMode is "${TRAINING_PLAN_EXECUTION_MODE}".`,
	        options: {
	            type: QueryBuilder_1.FieldType.Custom,
	            buildWhere: buildIsTrainingPlanWhere,
	            buildOrderBy: (direction) => [{ executionMode: direction }],
	        },
	        allowedOperators: ['is'],
	    },
	    createdAt: {
	        description: 'Plan creation time. Filter value is a millisecond timestamp.',
	        options: {
	            type: QueryBuilder_1.FieldType.Custom,
	            buildWhere: buildTimestampWhere('createdAt'),
	            buildOrderBy: (direction) => [{ createdAt: direction }],
	        },
	        allowedOperators: TIMESTAMP_ALLOWED_OPS,
	    },
	    deadlineAt: {
	        description: 'Plan deadline. Filter value is a millisecond timestamp.',
	        options: {
	            type: QueryBuilder_1.FieldType.Custom,
	            buildWhere: buildTimestampWhere('deadlineAt'),
	            buildOrderBy: (direction) => [{ deadlineAt: direction }],
	        },
	        allowedOperators: TIMESTAMP_ALLOWED_OPS,
	    },
	    completedAt: {
	        description: 'Plan completion time. Null for plans that have not yet completed. Filter value is a millisecond timestamp.',
	        options: {
	            type: QueryBuilder_1.FieldType.Custom,
	            buildWhere: buildTimestampWhere('completedAt'),
	            buildOrderBy: (direction) => [{ completedAt: direction }],
	        },
	        allowedOperators: [...TIMESTAMP_ALLOWED_OPS, 'isEmpty', 'isNotEmpty'],
	    },
	};
	adminPlanFilterSchema.adminPlanFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: {
	        id: {
	            description: 'Plan ID (a `CadstromUid`, e.g., "wizplan_…").',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'isAnyOf', 'startsWith', 'endsWith'],
	        },
	        packageId: {
	            description: 'ID of the package the plan operates on (a `CadstromUid`, e.g., "pkg_…"). Null for plans with no package.',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'isAnyOf', 'startsWith', 'endsWith'],
	        },
	        status: {
	            description: 'Plan status: "pending", "complete", "failed", or "cancelled".',
	            options: { type: QueryBuilder_1.FieldType.String },
	            allowedOperators: ['is', 'not', 'isAnyOf'],
	        },
	        generator: {
	            description: 'Generator name.',
	            options: { type: QueryBuilder_1.FieldType.String, column: 'generatorName' },
	            allowedOperators: ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'isAnyOf', 'startsWith', 'endsWith'],
	        },
	        assignedToUserName: {
	            description: 'Display name or username of the assigned user. Searches both displayName and username.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: buildPersonNameWhere('assignedToUser', 'assignedToUserId'),
	                buildOrderBy: (direction) => [{ assignedToUser: { displayName: direction } }],
	            },
	            allowedOperators: STRING_ALLOWED_OPS,
	        },
	        assignedToOrgName: {
	            description: 'Display name or username of the assigned organization. Searches both displayName and username.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: buildPersonNameWhere('assignedToOrg', 'assignedToOrgId'),
	                buildOrderBy: (direction) => [{ assignedToOrg: { displayName: direction } }],
	            },
	            allowedOperators: STRING_ALLOWED_OPS,
	        },
	        completedByUserNames: {
	            description: 'Display names or usernames of all users that completed a fragment of this plan.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: buildCompletedByUserNamesWhere,
	                buildOrderBy: () => [],
	            },
	            allowedOperators: ['contains', 'doesNotContain', 'isEmpty', 'isNotEmpty'],
	        },
	        associatedUserNames: {
	            description: 'Display names or usernames of "associated" users to the plan: the `completedBy` users of the plan\'s fragments, as well as the plan\'s `assignedToUserId`.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: buildAssociatedUserNameWhere,
	                buildOrderBy: (direction) => [{ assignedToUser: { displayName: direction } }],
	            },
	            allowedOperators: STRING_ALLOWED_OPS,
	        },
	        manufacturerPartNumber: {
	            description: 'Manufacturer part number of the linked package.',
	            options: {
	                type: QueryBuilder_1.FieldType.Custom,
	                buildWhere: (operator, value) => {
	                    if (operator === 'isEmpty') {
	                        return { packageId: null };
	                    }
	                    if (operator === 'isNotEmpty') {
	                        return { packageId: { not: null } };
	                    }
	                    const clause = (0, QueryBuilder_1.buildStringOperatorClause)('manufacturerPartNumber', operator, value);
	                    if (clause === null) {
	                        return {};
	                    }
	                    if (NEGATIVE_STRING_OPERATORS.includes(operator)) {
	                        return { OR: [{ packageId: null }, { package: { is: clause } }] };
	                    }
	                    return { package: { is: clause } };
	                },
	                buildOrderBy: (direction) => [{ package: { manufacturerPartNumber: direction } }],
	            },
	            allowedOperators: STRING_ALLOWED_OPS,
	        },
	        ...sharedPlanFields,
	    },
	    ignoredFields: ['actions'],
	});
	adminPlanFilterSchema.adminPlanCountFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
	    fields: { ...sharedPlanFields },
	    ignoredFields: [],
	});
	function buildIsTrainingPlanWhere(operator, value) {
	    const isTrue = value === 'true';
	    const isFalse = value === 'false';
	    const notTrainingPlan = { OR: [{ executionMode: null }, { executionMode: { not: TRAINING_PLAN_EXECUTION_MODE } }] };
	    switch (operator) {
	        case 'is':
	        case 'equals': {
	            if (isTrue) {
	                return { executionMode: TRAINING_PLAN_EXECUTION_MODE };
	            }
	            if (isFalse) {
	                return notTrainingPlan;
	            }
	            return {};
	        }
	        case 'not':
	        case 'doesNotEqual': {
	            if (isTrue) {
	                return notTrainingPlan;
	            }
	            if (isFalse) {
	                return { executionMode: TRAINING_PLAN_EXECUTION_MODE };
	            }
	            return {};
	        }
	        default:
	            return {};
	    }
	}
	function buildTimestampWhere(column) {
	    return (operator, value) => {
	        if (operator === 'isEmpty') {
	            return { [column]: null };
	        }
	        if (operator === 'isNotEmpty') {
	            return { [column]: { not: null } };
	        }
	        if (value === undefined || value === '') {
	            return {};
	        }
	        const ms = Number(value);
	        if (!Number.isFinite(ms)) {
	            return {};
	        }
	        const date = new Date(ms);
	        switch (operator) {
	            case 'equals':
	            case 'is':
	                return { [column]: date };
	            case 'not':
	            case 'doesNotEqual':
	                return { NOT: { [column]: date } };
	            case 'greaterThan':
	                return { [column]: { gt: date } };
	            case 'greaterThanOrEqual':
	                return { [column]: { gte: date } };
	            case 'lessThan':
	                return { [column]: { lt: date } };
	            case 'lessThanOrEqual':
	                return { [column]: { lte: date } };
	            default:
	                return {};
	        }
	    };
	}
	function buildPersonNameWhere(relation, idField) {
	    return (operator, value) => {
	        if (operator === 'isEmpty') {
	            return { [idField]: null };
	        }
	        if (operator === 'isNotEmpty') {
	            return { [idField]: { not: null } };
	        }
	        const buildPositiveOr = (op, opValue, extraProps) => ({
	            [relation]: {
	                is: {
	                    OR: [{ displayName: { [op]: opValue, ...extraProps } }, { username: { [op]: opValue, ...extraProps } }],
	                },
	            },
	        });
	        const buildNegativeAnd = (op, opValue, extraProps) => ({
	            OR: [
	                { [idField]: null },
	                {
	                    [relation]: {
	                        is: {
	                            AND: [
	                                { NOT: { displayName: { [op]: opValue, ...extraProps } } },
	                                { NOT: { username: { [op]: opValue, ...extraProps } } },
	                            ],
	                        },
	                    },
	                },
	            ],
	        });
	        switch (operator) {
	            case 'contains':
	                return buildPositiveOr('contains', value, { mode: 'insensitive' });
	            case 'doesNotContain':
	                return buildNegativeAnd('contains', value, { mode: 'insensitive' });
	            case 'equals':
	            case 'is':
	                return buildPositiveOr('equals', value);
	            case 'doesNotEqual':
	            case 'not':
	                return buildNegativeAnd('equals', value);
	            case 'startsWith':
	                return buildPositiveOr('startsWith', value, { mode: 'insensitive' });
	            case 'endsWith':
	                return buildPositiveOr('endsWith', value, { mode: 'insensitive' });
	            case 'isAnyOf': {
	                try {
	                    return buildPositiveOr('in', (0, QueryBuilder_1.parseIsAnyOfValues)(value));
	                }
	                catch {
	                    return {};
	                }
	            }
	            default:
	                return {};
	        }
	    };
	}
	function buildCompletedByUserNamesWhere(operator, value) {
	    if (operator === 'isEmpty') {
	        return { fragments: { none: { completedByUserId: { not: null } } } };
	    }
	    if (operator === 'isNotEmpty') {
	        return { fragments: { some: { completedByUserId: { not: null } } } };
	    }
	    const nameMatch = {
	        OR: [
	            { displayName: { contains: value, mode: 'insensitive' } },
	            { username: { contains: value, mode: 'insensitive' } },
	        ],
	    };
	    if (operator === 'contains') {
	        return { fragments: { some: { completedBy: { is: nameMatch } } } };
	    }
	    if (operator === 'doesNotContain') {
	        return { fragments: { none: { completedBy: { is: nameMatch } } } };
	    }
	    return {};
	}
	function buildAssociatedUserNameWhere(operator, value) {
	    if (operator === 'isEmpty') {
	        return { fragments: { none: { completedByUserId: { not: null } } }, assignedToUserId: null };
	    }
	    if (operator === 'isNotEmpty') {
	        return { OR: [{ fragments: { some: { completedByUserId: { not: null } } } }, { assignedToUserId: { not: null } }] };
	    }
	    const fragmentNameMatch = (op, val, extra) => ({
	        fragments: {
	            some: {
	                completedBy: { is: { OR: [{ displayName: { [op]: val, ...extra } }, { username: { [op]: val, ...extra } }] } },
	            },
	        },
	    });
	    const assignedUserNameMatch = (op, val, extra) => ({
	        assignedToUser: { is: { OR: [{ displayName: { [op]: val, ...extra } }, { username: { [op]: val, ...extra } }] } },
	    });
	    const assignedUserNameNoMatch = (op, val, extra) => ({
	        assignedToUser: {
	            is: { AND: [{ NOT: { displayName: { [op]: val, ...extra } } }, { NOT: { username: { [op]: val, ...extra } } }] },
	        },
	    });
	    const buildPositive = (op, val, extra) => ({
	        OR: [
	            fragmentNameMatch(op, val, extra),
	            {
	                fragments: { none: { completedByUserId: { not: null } } },
	                ...assignedUserNameMatch(op, val, extra),
	            },
	        ],
	    });
	    const buildNegative = (op, val, extra) => ({
	        AND: [
	            {
	                fragments: {
	                    none: {
	                        completedBy: {
	                            is: { OR: [{ displayName: { [op]: val, ...extra } }, { username: { [op]: val, ...extra } }] },
	                        },
	                    },
	                },
	            },
	            {
	                OR: [
	                    { fragments: { some: { completedByUserId: { not: null } } } },
	                    { assignedToUserId: null },
	                    assignedUserNameNoMatch(op, val, extra),
	                ],
	            },
	        ],
	    });
	    switch (operator) {
	        case 'contains':
	            return buildPositive('contains', value, { mode: 'insensitive' });
	        case 'doesNotContain':
	            return buildNegative('contains', value, { mode: 'insensitive' });
	        case 'equals':
	        case 'is':
	            return buildPositive('equals', value);
	        case 'doesNotEqual':
	        case 'not':
	            return buildNegative('equals', value);
	        case 'startsWith':
	            return buildPositive('startsWith', value, { mode: 'insensitive' });
	        case 'endsWith':
	            return buildPositive('endsWith', value, { mode: 'insensitive' });
	        case 'isAnyOf': {
	            try {
	                return buildPositive('in', (0, QueryBuilder_1.parseIsAnyOfValues)(value));
	            }
	            catch {
	                return {};
	            }
	        }
	        default:
	            return {};
	    }
	}
	
	return adminPlanFilterSchema;
}

var fragmentTypes = {};

var hasRequiredFragmentTypes;

function requireFragmentTypes () {
	if (hasRequiredFragmentTypes) return fragmentTypes;
	hasRequiredFragmentTypes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.FINAL_FRAGMENT_TYPES = void 0;
		exports.isFinalFragmentType = isFinalFragmentType;
		exports.FINAL_FRAGMENT_TYPES = new Set(['packageUnderstanding']);
		function isFinalFragmentType(name) {
		    return exports.FINAL_FRAGMENT_TYPES.has(name);
		}
		
	} (fragmentTypes));
	return fragmentTypes;
}

var extractFloatsAndTies_types = {};

var hasRequiredExtractFloatsAndTies_types;

function requireExtractFloatsAndTies_types () {
	if (hasRequiredExtractFloatsAndTies_types) return extractFloatsAndTies_types;
	hasRequiredExtractFloatsAndTies_types = 1;
	Object.defineProperty(extractFloatsAndTies_types, "__esModule", { value: true });
	extractFloatsAndTies_types.FloatsAndTiesSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const zod_1 = /*@__PURE__*/ requireZod();
	extractFloatsAndTies_types.FloatsAndTiesSchema = zod_1.default.record(zod_1.default.string(), zod_1.default.object({
	    floatingBehavior: zod_1.default.nativeEnum(sigma_engine_shared_1.SignalFloatingBehavior).optional(),
	    ties: sigma_engine_shared_1.IExternalComponentSchema.array().optional(),
	}));
	
	return extractFloatsAndTies_types;
}

var form_types = {};

var hasRequiredForm_types;

function requireForm_types () {
	if (hasRequiredForm_types) return form_types;
	hasRequiredForm_types = 1;
	Object.defineProperty(form_types, "__esModule", { value: true });
	form_types.FormBailoutSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	form_types.FormBailoutSchema = z.object({
	    bailedOut: z.boolean(),
	    stepAtBailout: z.number().int().optional(),
	});
	
	return form_types;
}

var packageCommon = {};

var hasRequiredPackageCommon;

function requirePackageCommon () {
	if (hasRequiredPackageCommon) return packageCommon;
	hasRequiredPackageCommon = 1;
	Object.defineProperty(packageCommon, "__esModule", { value: true });
	packageCommon.SigmaModelTypeSchema = packageCommon.DatasheetUriSchema = packageCommon.PinoutSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	packageCommon.PinoutSchema = z.record(z.string(), z.array(z.string()));
	packageCommon.DatasheetUriSchema = z.string();
	packageCommon.SigmaModelTypeSchema = z.string();
	
	return packageCommon;
}

var form = {};

var hasRequiredForm;

function requireForm () {
	if (hasRequiredForm) return form;
	hasRequiredForm = 1;
	Object.defineProperty(form, "__esModule", { value: true });
	
	return form;
}

var normalizedRatingsForm = {};

var payload$2 = {};

var hasRequiredPayload$2;

function requirePayload$2 () {
	if (hasRequiredPayload$2) return payload$2;
	hasRequiredPayload$2 = 1;
	Object.defineProperty(payload$2, "__esModule", { value: true });
	payload$2.NormalizedRatingsFormPayloadSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const parent_rating_row_1 = requireParentRatingRow();
	const stored_ratings_table_1 = requireStoredRatingsTable();
	payload$2.NormalizedRatingsFormPayloadSchema = z.object({
	    boundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
	    modes: sigma_engine_shared_1.ModeBaseSchema.array(),
	    differentialPairs: sigma_engine_shared_1.DifferentialPairSchema.array(),
	    table: stored_ratings_table_1.StoredRatingsTableSchema.nullable(),
	    parentRows: parent_rating_row_1.ParentRatingRowSchema.array(),
	});
	
	return payload$2;
}

var formSchemas$2 = {};

var formLatest = {};

var hasRequiredFormLatest;

function requireFormLatest () {
	if (hasRequiredFormLatest) return formLatest;
	hasRequiredFormLatest = 1;
	Object.defineProperty(formLatest, "__esModule", { value: true });
	formLatest.NORMALIZED_RATINGS_FORM_LATEST = formLatest.NormalizedRatingsFormCodec = formLatest.BoundNormalizedRatingsForm = void 0;
	const ratings_table_codec_1 = requireRatingsTableCodec();
	const ratings_table_types_1 = requireRatingsTable_types();
	const stored_ratings_table_1 = requireStoredRatingsTable();
	class BoundNormalizedRatingsForm {
	    constructor(seed) {
	        this.seed = seed;
	    }
	    toFormData(payload) {
	        const table = this.open(payload);
	        return { formVersion: table.formVersion, kind: table.kind, signalGroups: table.signalGroups, rows: table.rows };
	    }
	    toCanonical(table) {
	        const result = this.save(table);
	        if (result.outcome === ratings_table_types_1.RatingsTableSaveOutcome.Refused) {
	            throw new Error(`Rows need a "measured from" before the table can be saved: ${result.rowsNeedingReference.join(', ')}`);
	        }
	        return result.payload;
	    }
	    save(table) {
	        const opened = this.open(this.seed);
	        const result = ratings_table_codec_1.RatingsTableCodec.toBoundaries(this.seed.kind, BoundNormalizedRatingsForm.signalsWithPairs(this.seed.signals, this.seed.differentialPairs), this.seed.modes, this.seed.boundaries, { ...table, conditions: opened.conditions, pairs: opened.pairs }, { deletedBoundaryIds: [], dropUnstatedRowBounds: true }, this.seed.parentRows);
	        if (result.outcome === ratings_table_types_1.RatingsTableSaveOutcome.Refused) {
	            return result;
	        }
	        return {
	            outcome: ratings_table_types_1.RatingsTableSaveOutcome.Saved,
	            payload: {
	                boundaries: result.boundaries,
	                modes: result.modes,
	                differentialPairs: this.seed.differentialPairs,
	                table: result.table,
	                parentRows: result.parentRows,
	            },
	        };
	    }
	    open(payload) {
	        return ratings_table_codec_1.RatingsTableCodec.toRatingsTable(this.seed.kind, BoundNormalizedRatingsForm.signalsWithPairs(this.seed.signals, payload.differentialPairs), payload.modes, payload.boundaries, payload.table, payload.parentRows);
	    }
	    static signalsWithPairs(signals, pairs) {
	        return signals.map((signal) => {
	            const pair = pairs.find((candidate) => String(candidate.positiveId) === String(signal.id) || String(candidate.negativeId) === String(signal.id));
	            return pair === undefined ?
	                { id: signal.id, name: signal.name }
	                : {
	                    id: signal.id,
	                    name: signal.name,
	                    diffPair: { pairName: pair.pairName, positiveId: pair.positiveId, negativeId: pair.negativeId },
	                };
	        });
	    }
	}
	formLatest.BoundNormalizedRatingsForm = BoundNormalizedRatingsForm;
	class NormalizedRatingsFormCodec {
	    constructor() {
	        this.version = stored_ratings_table_1.RATINGS_TABLE_FORM_VERSION;
	        this.formDataSchema = stored_ratings_table_1.StoredRatingsTableSchema;
	    }
	    bind(seed) {
	        return new BoundNormalizedRatingsForm(seed);
	    }
	}
	formLatest.NormalizedRatingsFormCodec = NormalizedRatingsFormCodec;
	formLatest.NORMALIZED_RATINGS_FORM_LATEST = new NormalizedRatingsFormCodec();
	
	return formLatest;
}

var hasRequiredFormSchemas$2;

function requireFormSchemas$2 () {
	if (hasRequiredFormSchemas$2) return formSchemas$2;
	hasRequiredFormSchemas$2 = 1;
	(function (exports) {
		var __createBinding = (formSchemas$2 && formSchemas$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (formSchemas$2 && formSchemas$2.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireFormLatest(), exports);
		
	} (formSchemas$2));
	return formSchemas$2;
}

var stepDefinition$2 = {};

var hasRequiredStepDefinition$2;

function requireStepDefinition$2 () {
	if (hasRequiredStepDefinition$2) return stepDefinition$2;
	hasRequiredStepDefinition$2 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NormalizedOcsFormDefinition = exports.NormalizedAmrsFormDefinition = exports.NormalizedRatingsFormOutputSchema = exports.NormalizedRatingsFormOpenedOnSchema = exports.NormalizedRatingsFormInputSchema = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const z = /*@__PURE__*/ requireZod();
		const misc_1 = requireMisc();
		const payload_1 = requirePayload$2();
		exports.NormalizedRatingsFormInputSchema = payload_1.NormalizedRatingsFormPayloadSchema.extend({
		    signals: sigma_engine_shared_1.SignalSchema.array(),
		    datasheetFileId: z.string(),
		    manufacturerPartNumber: z.string(),
		    sequenceGate: misc_1.sequenceGateSchema,
		});
		exports.NormalizedRatingsFormOpenedOnSchema = z.object({
		    modes: sigma_engine_shared_1.ModeBaseSchema.array(),
		    differentialPairs: sigma_engine_shared_1.DifferentialPairSchema.array(),
		});
		exports.NormalizedRatingsFormOutputSchema = payload_1.NormalizedRatingsFormPayloadSchema.extend({
		    openedOn: exports.NormalizedRatingsFormOpenedOnSchema.optional(),
		});
		exports.NormalizedAmrsFormDefinition = {
		    type: 'NormalizedAmrsForm',
		    displayName: 'Normalized absolute maximum ratings',
		    inputSchema: exports.NormalizedRatingsFormInputSchema,
		    outputSchema: exports.NormalizedRatingsFormOutputSchema,
		};
		exports.NormalizedOcsFormDefinition = {
		    type: 'NormalizedOcsForm',
		    displayName: 'Normalized operating conditions',
		    inputSchema: exports.NormalizedRatingsFormInputSchema,
		    outputSchema: exports.NormalizedRatingsFormOutputSchema,
		};
		
	} (stepDefinition$2));
	return stepDefinition$2;
}

var hasRequiredNormalizedRatingsForm;

function requireNormalizedRatingsForm () {
	if (hasRequiredNormalizedRatingsForm) return normalizedRatingsForm;
	hasRequiredNormalizedRatingsForm = 1;
	(function (exports) {
		var __createBinding = (normalizedRatingsForm && normalizedRatingsForm.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (normalizedRatingsForm && normalizedRatingsForm.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePayload$2(), exports);
		__exportStar(requireFormSchemas$2(), exports);
		__exportStar(requireStepDefinition$2(), exports);
		
	} (normalizedRatingsForm));
	return normalizedRatingsForm;
}

var normalizedSignalDriveModesForm = {};

var payload$1 = {};

var hasRequiredPayload$1;

function requirePayload$1 () {
	if (hasRequiredPayload$1) return payload$1;
	hasRequiredPayload$1 = 1;
	Object.defineProperty(payload$1, "__esModule", { value: true });
	payload$1.NormalizedSignalDriveModesFormPayloadSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	payload$1.NormalizedSignalDriveModesFormPayloadSchema = z.object({
	    signals: sigma_engine_shared_1.SignalSchema.array(),
	    modes: sigma_engine_shared_1.ModeBaseSchema.array(),
	});
	
	return payload$1;
}

var formSchemas$1 = {};

var hasRequiredFormSchemas$1;

function requireFormSchemas$1 () {
	if (hasRequiredFormSchemas$1) return formSchemas$1;
	hasRequiredFormSchemas$1 = 1;
	(function (exports) {
		var __createBinding = (formSchemas$1 && formSchemas$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (formSchemas$1 && formSchemas$1.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NormalizedSignalDriveModesFormDataSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const form_latest_1 = requireFormLatest$1();
		__exportStar(requireFormLatest$1(), exports);
		exports.NormalizedSignalDriveModesFormDataSchema = z.discriminatedUnion('formVersion', [
		    form_latest_1.NormalizedSignalDriveModesFormStateSchema,
		]);
		
	} (formSchemas$1));
	return formSchemas$1;
}

var stepDefinition$1 = {};

var hasRequiredStepDefinition$1;

function requireStepDefinition$1 () {
	if (hasRequiredStepDefinition$1) return stepDefinition$1;
	hasRequiredStepDefinition$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.NormalizedSignalDriveModesFormDefinition = exports.NormalizedSignalDriveModesFormOutputSchema = exports.NormalizedSignalDriveModesFormInputSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const misc_1 = requireMisc();
		const form_schemas_1 = requireFormSchemas$1();
		const payload_1 = requirePayload$1();
		exports.NormalizedSignalDriveModesFormInputSchema = payload_1.NormalizedSignalDriveModesFormPayloadSchema.extend({
		    datasheetFileId: z.string(),
		    manufacturerPartNumber: z.string(),
		    sequenceGate: misc_1.sequenceGateSchema,
		});
		exports.NormalizedSignalDriveModesFormOutputSchema = payload_1.NormalizedSignalDriveModesFormPayloadSchema.extend({
		    rawFormData: form_schemas_1.NormalizedSignalDriveModesFormDataSchema.optional().catch(undefined),
		});
		exports.NormalizedSignalDriveModesFormDefinition = {
		    type: 'NormalizedSignalDriveModesForm',
		    displayName: 'Normalized signal drive modes',
		    inputSchema: exports.NormalizedSignalDriveModesFormInputSchema,
		    outputSchema: exports.NormalizedSignalDriveModesFormOutputSchema,
		};
		
	} (stepDefinition$1));
	return stepDefinition$1;
}

var hasRequiredNormalizedSignalDriveModesForm;

function requireNormalizedSignalDriveModesForm () {
	if (hasRequiredNormalizedSignalDriveModesForm) return normalizedSignalDriveModesForm;
	hasRequiredNormalizedSignalDriveModesForm = 1;
	(function (exports) {
		var __createBinding = (normalizedSignalDriveModesForm && normalizedSignalDriveModesForm.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (normalizedSignalDriveModesForm && normalizedSignalDriveModesForm.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePayload$1(), exports);
		__exportStar(requireFormSchemas$1(), exports);
		__exportStar(requireStepDefinition$1(), exports);
		
	} (normalizedSignalDriveModesForm));
	return normalizedSignalDriveModesForm;
}

var sigmaModelEntryForm = {};

var formSchemas = {};

var formV1 = {};

var hasRequiredFormV1;

function requireFormV1 () {
	if (hasRequiredFormV1) return formV1;
	hasRequiredFormV1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SigmaModelEntryFormStateV1Schema = exports.FormPinAssignmentV1Schema = exports.PinBlockRefV1Schema = exports.PkgParameterV1Schema = exports.SIGMA_MODEL_ENTRY_FORM_V1_VERSION = void 0;
		exports.migrateSigmaModelEntryFormV1ToLatest = migrateSigmaModelEntryFormV1ToLatest;
		const z = /*@__PURE__*/ requireZod();
		const form_latest_1 = requireFormLatest$2();
		exports.SIGMA_MODEL_ENTRY_FORM_V1_VERSION = 'sigma-model-entry-form/v1';
		exports.PkgParameterV1Schema = z.object({
		    value: z.string().nullable(),
		    variance: z.string().nullable(),
		});
		exports.PinBlockRefV1Schema = z.object({
		    name: z.string().min(1),
		    index: z.number().int().nonnegative(),
		});
		exports.FormPinAssignmentV1Schema = z.object({
		    modelSignalId: z.string(),
		    block: exports.PinBlockRefV1Schema.nullable(),
		    nameOverride: z.string().nullable(),
		    pins: z.string().array(),
		    packageSignalId: z.string().optional(),
		});
		exports.SigmaModelEntryFormStateV1Schema = z.object({
		    formVersion: z.literal(exports.SIGMA_MODEL_ENTRY_FORM_V1_VERSION),
		    parentSigmaModelType: z.string(),
		    elementSigmaModelType: z.string().nullable(),
		    pinCount: z.number().int().nullable(),
		    arrayConfirmed: z.boolean().nullable(),
		    pinAssignments: exports.FormPinAssignmentV1Schema.array(),
		    ncPins: z.string().array(),
		    parameters: z.record(z.string(), exports.PkgParameterV1Schema),
		});
		function migrateSigmaModelEntryFormV1ToLatest(formData) {
		    return {
		        ...formData,
		        formVersion: form_latest_1.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
		        pinAssignments: formData.pinAssignments.map((assignment) => ({ ...assignment })),
		        ncPins: [...formData.ncPins],
		    };
		}
		
	} (formV1));
	return formV1;
}

var hasRequiredFormSchemas;

function requireFormSchemas () {
	if (hasRequiredFormSchemas) return formSchemas;
	hasRequiredFormSchemas = 1;
	(function (exports) {
		var __createBinding = (formSchemas && formSchemas.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (formSchemas && formSchemas.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SIGMA_MODEL_ENTRY_FORM_LATEST = exports.SigmaModelEntryFormDataSchema = void 0;
		exports.upgradeSigmaModelEntryFormData = upgradeSigmaModelEntryFormData;
		exports.parseSigmaModelEntryFormDataToLatest = parseSigmaModelEntryFormDataToLatest;
		const z = /*@__PURE__*/ requireZod();
		const form_latest_1 = requireFormLatest$2();
		const form_v1_1 = requireFormV1();
		__exportStar(requireFormLatest$2(), exports);
		__exportStar(requireFormV1(), exports);
		exports.SigmaModelEntryFormDataSchema = z.discriminatedUnion('formVersion', [
		    form_v1_1.SigmaModelEntryFormStateV1Schema,
		    form_latest_1.SigmaModelEntryFormStateSchema,
		]);
		function upgradeSigmaModelEntryFormData(formData) {
		    return formData.formVersion === form_v1_1.SIGMA_MODEL_ENTRY_FORM_V1_VERSION ?
		        (0, form_v1_1.migrateSigmaModelEntryFormV1ToLatest)(formData)
		        : formData;
		}
		function parseSigmaModelEntryFormDataToLatest(rawFormData) {
		    const parsed = exports.SigmaModelEntryFormDataSchema.safeParse(rawFormData);
		    return parsed.success ? upgradeSigmaModelEntryFormData(parsed.data) : null;
		}
		exports.SIGMA_MODEL_ENTRY_FORM_LATEST = form_latest_1.sigmaModelEntryFormLatest;
		
	} (formSchemas));
	return formSchemas;
}

var arrayFormOutput = {};

var hasRequiredArrayFormOutput;

function requireArrayFormOutput () {
	if (hasRequiredArrayFormOutput) return arrayFormOutput;
	hasRequiredArrayFormOutput = 1;
	Object.defineProperty(arrayFormOutput, "__esModule", { value: true });
	
	return arrayFormOutput;
}

var modelSignalUniqueness = {};

var hasRequiredModelSignalUniqueness;

function requireModelSignalUniqueness () {
	if (hasRequiredModelSignalUniqueness) return modelSignalUniqueness;
	hasRequiredModelSignalUniqueness = 1;
	Object.defineProperty(modelSignalUniqueness, "__esModule", { value: true });
	modelSignalUniqueness.DuplicateModelSignalMappingError = void 0;
	modelSignalUniqueness.findDuplicateModelSignalMappings = findDuplicateModelSignalMappings;
	modelSignalUniqueness.assertUniqueModelSignalMappings = assertUniqueModelSignalMappings;
	const array_submodels_1 = requireArraySubmodels();
	function findDuplicateModelSignalMappings(args) {
	    const definition = args.getFormDefinition(args.parentType);
	    if (definition === null) {
	        return [];
	    }
	    const singleUse = new Set(definition.signals
	        .filter((signal) => signal.allowMultiMapping !== true && signal.repeatableBlock === null)
	        .map((signal) => String(signal.id)));
	    const elementConfig = args.elementType === '' ? null : (0, array_submodels_1.getArrayElementConfig)(args.elementType);
	    const elementTerminalName = elementConfig === null ? null : new RegExp(`^${elementConfig.prefix}\\d+_`);
	    const elementTerminals = elementConfig === null ? undefined : (args.getFormDefinition(args.elementType)?.signals.map((signal) => String(signal.id)));
	    const isElementTerminal = (name, ref) => elementTerminalName?.test(name) === true && (elementTerminals === undefined || elementTerminals.includes(ref));
	    const holders = new Map();
	    for (const signal of args.signals) {
	        if (signal.modelSignal === undefined || signal.modelSignal === null) {
	            continue;
	        }
	        const ref = String(signal.modelSignal);
	        if (!singleUse.has(ref) || isElementTerminal(signal.name, ref)) {
	            continue;
	        }
	        const names = holders.get(ref);
	        if (names === undefined) {
	            holders.set(ref, [signal.name]);
	        }
	        else {
	            names.push(signal.name);
	        }
	    }
	    const toleratedHolders = new Map((args.tolerated ?? []).map((mapping) => [mapping.modelSignal, new Set(mapping.signalNames)]));
	    return [...holders]
	        .filter(([modelSignal, signalNames]) => {
	        const before = toleratedHolders.get(modelSignal);
	        return signalNames.length > 1 && (before === undefined || signalNames.some((name) => !before.has(name)));
	    })
	        .map(([modelSignal, signalNames]) => ({ modelSignal, signalNames }));
	}
	function assertUniqueModelSignalMappings(args) {
	    const duplicates = findDuplicateModelSignalMappings(args);
	    if (duplicates.length > 0) {
	        throw new DuplicateModelSignalMappingError(duplicates, args.parentType);
	    }
	}
	class DuplicateModelSignalMappingError extends Error {
	    constructor(mappings, sigmaModelType) {
	        const described = mappings
	            .map((mapping) => `Model signal '${mapping.modelSignal}' is mapped to ${mapping.signalNames.join(', ')}.`)
	            .join(' ');
	        super(`${described} The ${sigmaModelType} model takes one package signal on each of these, so the ` +
	            'simulation could not tell them apart. Remap or unmap all but one.');
	        this.name = 'DuplicateModelSignalMappingError';
	        this.mappings = mappings;
	    }
	}
	modelSignalUniqueness.DuplicateModelSignalMappingError = DuplicateModelSignalMappingError;
	
	return modelSignalUniqueness;
}

var pinDesignatorRules = {};

var hasRequiredPinDesignatorRules;

function requirePinDesignatorRules () {
	if (hasRequiredPinDesignatorRules) return pinDesignatorRules;
	hasRequiredPinDesignatorRules = 1;
	Object.defineProperty(pinDesignatorRules, "__esModule", { value: true });
	pinDesignatorRules.isNumericPinDesignator = isNumericPinDesignator;
	pinDesignatorRules.usesLabelledPinDesignators = usesLabelledPinDesignators;
	pinDesignatorRules.collectUniqueAssignedPinDesignators = collectUniqueAssignedPinDesignators;
	pinDesignatorRules.countUniqueAssignedPinDesignators = countUniqueAssignedPinDesignators;
	pinDesignatorRules.collectUniqueAssignedPackageSignalPinDesignators = collectUniqueAssignedPackageSignalPinDesignators;
	pinDesignatorRules.countUniqueAssignedPackageSignalPinDesignators = countUniqueAssignedPackageSignalPinDesignators;
	pinDesignatorRules.collectUniqueAssignedFormPinDesignators = collectUniqueAssignedFormPinDesignators;
	pinDesignatorRules.countUniqueAssignedFormPinDesignators = countUniqueAssignedFormPinDesignators;
	pinDesignatorRules.findOutOfRangePinDesignators = findOutOfRangePinDesignators;
	pinDesignatorRules.formatOutOfRangePinError = formatOutOfRangePinError;
	pinDesignatorRules.formatPinCountMismatchError = formatPinCountMismatchError;
	pinDesignatorRules.formatUnmappedTerminalMessage = formatUnmappedTerminalMessage;
	pinDesignatorRules.formatUnmappedSignalPinoutError = formatUnmappedSignalPinoutError;
	const pin_count_rules_1 = requirePinCountRules();
	function isNumericPinDesignator(designator) {
	    return /^\d+$/.test(designator.trim());
	}
	function usesLabelledPinDesignators(designators) {
	    for (const designator of designators) {
	        if (designator.trim() !== '' && !isNumericPinDesignator(designator)) {
	            return true;
	        }
	    }
	    return false;
	}
	function isActiveModelSignalForPinCount(modelSignal, pinout) {
	    if (!modelSignal.isOptional) {
	        return true;
	    }
	    return (0, pin_count_rules_1.normalizePinDesignators)(pinout[String(modelSignal.id)] ?? []).length > 0;
	}
	function collectUniqueAssignedPinDesignators(modelSignals, pinout, ncPinNumbers = []) {
	    const uniquePins = new Set();
	    for (const modelSignal of modelSignals) {
	        if (!isActiveModelSignalForPinCount(modelSignal, pinout)) {
	            continue;
	        }
	        for (const pin of (0, pin_count_rules_1.normalizePinDesignators)(pinout[String(modelSignal.id)] ?? [])) {
	            uniquePins.add(pin);
	        }
	    }
	    for (const pin of (0, pin_count_rules_1.normalizePinDesignators)(ncPinNumbers)) {
	        uniquePins.add(pin);
	    }
	    return uniquePins;
	}
	function countUniqueAssignedPinDesignators(modelSignals, pinout, ncPinNumbers = []) {
	    return collectUniqueAssignedPinDesignators(modelSignals, pinout, ncPinNumbers).size;
	}
	function collectUniqueAssignedPackageSignalPinDesignators(packageSignals, pinout, ncPinNumbers = []) {
	    const uniquePins = new Set();
	    for (const signal of packageSignals) {
	        for (const pin of (0, pin_count_rules_1.normalizePinDesignators)(pinout[String(signal.id)] ?? [])) {
	            uniquePins.add(pin);
	        }
	    }
	    for (const pin of (0, pin_count_rules_1.normalizePinDesignators)(ncPinNumbers)) {
	        uniquePins.add(pin);
	    }
	    return uniquePins;
	}
	function countUniqueAssignedPackageSignalPinDesignators(packageSignals, pinout, ncPinNumbers = []) {
	    return collectUniqueAssignedPackageSignalPinDesignators(packageSignals, pinout, ncPinNumbers).size;
	}
	function collectUniqueAssignedFormPinDesignators(modelSignals, packageSignals, pinout, ncPinNumbers = []) {
	    return new Set([
	        ...collectUniqueAssignedPinDesignators(modelSignals, pinout, ncPinNumbers),
	        ...collectUniqueAssignedPackageSignalPinDesignators(packageSignals, pinout, ncPinNumbers),
	    ]);
	}
	function countUniqueAssignedFormPinDesignators(modelSignals, packageSignals, pinout, ncPinNumbers = []) {
	    return collectUniqueAssignedFormPinDesignators(modelSignals, packageSignals, pinout, ncPinNumbers).size;
	}
	function findOutOfRangePinDesignators(assignedPins, pinCount) {
	    const outOfRange = [];
	    for (const pin of assignedPins) {
	        if (!isNumericPinDesignator(pin)) {
	            outOfRange.push(pin);
	            continue;
	        }
	        const value = Number.parseInt(pin, 10);
	        if (value < 1 || value > pinCount) {
	            outOfRange.push(pin);
	        }
	    }
	    return outOfRange.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
	}
	function formatOutOfRangePinError(outOfRangePins, pinCount) {
	    const pinWord = outOfRangePins.length === 1 ? 'pin' : 'pins';
	    return `Remove or correct ${pinWord} ${outOfRangePins.join(', ')} — this package only has pins 1–${pinCount}.`;
	}
	function formatPinCountMismatchError(pinCount, assignedPinCount) {
	    if (assignedPinCount === 0) {
	        return `Assign all pins to a signal or no connect. This package has ${pinCount} pins — none are assigned yet.`;
	    }
	    if (assignedPinCount < pinCount) {
	        const stillMissing = pinCount - assignedPinCount;
	        const missingWord = stillMissing === 1 ? 'pin' : 'pins';
	        return (`Assign all pins to a signal or no connect. This package has ${pinCount} pins — ` +
	            `only ${assignedPinCount} assigned, ${stillMissing} ${missingWord} still missing.`);
	    }
	    return (`This package has ${pinCount} pins, but ${assignedPinCount} distinct pin numbers are assigned. ` +
	        `Remove the extra pins, or fix any that fall outside 1–${pinCount}.`);
	}
	function formatUnmappedTerminalMessage(elementId, terminalName) {
	    if (elementId !== null) {
	        return `Array element ${elementId}: assign a pin to terminal "${terminalName}".`;
	    }
	    return `Required signal "${terminalName}" has no pin numbers assigned.`;
	}
	function formatUnmappedSignalPinoutError(signalName, arraySubmit) {
	    if (arraySubmit) {
	        const arrayElementMatch = /^([A-Z]+\d+)_(.+)$/.exec(signalName);
	        const elementId = arrayElementMatch?.[1];
	        const terminalName = arrayElementMatch?.[2];
	        if (elementId !== undefined && terminalName !== undefined) {
	            return formatUnmappedTerminalMessage(elementId, terminalName);
	        }
	    }
	    return formatUnmappedTerminalMessage(null, signalName);
	}
	
	return pinDesignatorRules;
}

var formCompletionRules = {};

var hasRequiredFormCompletionRules;

function requireFormCompletionRules () {
	if (hasRequiredFormCompletionRules) return formCompletionRules;
	hasRequiredFormCompletionRules = 1;
	Object.defineProperty(formCompletionRules, "__esModule", { value: true });
	formCompletionRules.requiredSignalsHavePins = requiredSignalsHavePins;
	formCompletionRules.modelPinoutCoversSignals = modelPinoutCoversSignals;
	formCompletionRules.missingRequiredParameterNames = missingRequiredParameterNames;
	formCompletionRules.requiredParametersSet = requiredParametersSet;
	formCompletionRules.arrayPinoutComplete = arrayPinoutComplete;
	formCompletionRules.arrayFormCompletion = arrayFormCompletion;
	formCompletionRules.repeatableBlockPinoutComplete = repeatableBlockPinoutComplete;
	formCompletionRules.repeatableBlockFormCompletion = repeatableBlockFormCompletion;
	formCompletionRules.simpleFormCompletion = simpleFormCompletion;
	const no_connect_signal_1 = requireNoConnectSignal();
	function normalizePins(pins) {
	    return pins.map((pin) => pin.trim()).filter((pin) => pin !== '');
	}
	function pinsForSignal(pinout, signalId) {
	    return normalizePins(pinout[signalId] ?? []);
	}
	function assignedFunctionalDesignators(pinout) {
	    const designators = new Set();
	    for (const [signalId, pins] of Object.entries(pinout)) {
	        if (signalId === no_connect_signal_1.NO_CONNECT_SIGNAL_ID) {
	            continue;
	        }
	        for (const pin of normalizePins(pins)) {
	            designators.add(pin);
	        }
	    }
	    return designators;
	}
	function noConnectPinsOverlap(pinout, functionalDesignators) {
	    return pinsForSignal(pinout, no_connect_signal_1.NO_CONNECT_SIGNAL_ID).some((pin) => functionalDesignators.has(pin));
	}
	function requiredSignalsHavePins(modelSignals, pinout) {
	    return modelSignals
	        .filter((signal) => !signal.isOptional)
	        .every((signal) => pinsForSignal(pinout, String(signal.id)).length > 0);
	}
	function modelPinoutCoversSignals(modelSignals, pinout, formSignals = []) {
	    if (!requiredSignalsHavePins(modelSignals, pinout)) {
	        return false;
	    }
	    const activeDesignators = new Set();
	    for (const signal of modelSignals) {
	        const pins = pinsForSignal(pinout, String(signal.id));
	        if (signal.isOptional && pins.length === 0) {
	            continue;
	        }
	        for (const pin of pins) {
	            activeDesignators.add(pin);
	        }
	    }
	    const modelSignalIds = new Set(modelSignals.map((signal) => String(signal.id)));
	    for (const signal of formSignals) {
	        const signalId = String(signal.id);
	        if (signalId === no_connect_signal_1.NO_CONNECT_SIGNAL_ID || modelSignalIds.has(signalId)) {
	            continue;
	        }
	        for (const pin of pinsForSignal(pinout, signalId)) {
	            activeDesignators.add(pin);
	        }
	    }
	    return (activeDesignators.size === assignedFunctionalDesignators(pinout).size &&
	        !noConnectPinsOverlap(pinout, activeDesignators));
	}
	function missingRequiredParameterNames(modelParameters, parameters) {
	    return modelParameters
	        .filter((parameter) => parameter.required)
	        .filter((parameter) => {
	        const entry = parameters[parameter.name];
	        return entry?.notSpecified !== true && (entry?.value?.trim() ?? '') === '';
	    })
	        .map((parameter) => parameter.name);
	}
	function requiredParametersSet(modelParameters, parameters) {
	    return missingRequiredParameterNames(modelParameters, parameters).length === 0;
	}
	function arrayPinoutComplete(args) {
	    const { formSignals, pinout, elementSignals, elementCount, submodelPrefix } = args;
	    const requiredSignalNames = new Set();
	    const optionalSignalNames = new Set();
	    for (let index = 1; index <= elementCount; index += 1) {
	        const subModelId = `${submodelPrefix}${index}`;
	        for (const elementSignal of elementSignals) {
	            const compositeName = `${subModelId}_${elementSignal.name}`;
	            (elementSignal.isOptional ? optionalSignalNames : requiredSignalNames).add(compositeName);
	        }
	    }
	    const signalsByName = new Map(formSignals.map((signal) => [signal.name, signal]));
	    for (const requiredName of requiredSignalNames) {
	        const signal = signalsByName.get(requiredName);
	        if (signal === undefined) {
	            return false;
	        }
	        if (pinsForSignal(pinout, String(signal.id)).length === 0) {
	            return false;
	        }
	    }
	    const activeDesignators = new Set();
	    for (const signal of formSignals) {
	        const signalId = String(signal.id);
	        if (signalId === no_connect_signal_1.NO_CONNECT_SIGNAL_ID) {
	            continue;
	        }
	        const pins = pinsForSignal(pinout, signalId);
	        if (optionalSignalNames.has(signal.name) && pins.length === 0) {
	            continue;
	        }
	        for (const pin of pins) {
	            activeDesignators.add(pin);
	        }
	    }
	    return (activeDesignators.size === assignedFunctionalDesignators(pinout).size &&
	        !noConnectPinsOverlap(pinout, activeDesignators));
	}
	function arrayFormCompletion(args) {
	    const { elementShape } = args;
	    if (elementShape === null) {
	        return { pinoutComplete: false, requiredParamsSet: false };
	    }
	    return {
	        pinoutComplete: arrayPinoutComplete({
	            formSignals: args.formSignals,
	            pinout: args.pinout,
	            elementSignals: elementShape.signals,
	            elementCount: args.elementCount,
	            submodelPrefix: args.submodelPrefix,
	        }),
	        requiredParamsSet: requiredParametersSet(elementShape.parameters, args.parameters),
	    };
	}
	function repeatableBlockPinoutComplete(args) {
	    const { formSignals, pinout, modelSignals } = args;
	    const optionalByModelSignalId = new Map(modelSignals.map((signal) => [String(signal.id), signal.isOptional]));
	    const packageSignalShapes = formSignals.map((signal) => ({
	        id: signal.id,
	        name: signal.name,
	        isOptional: optionalByModelSignalId.get(String(signal.modelSignal ?? '')) ?? false,
	    }));
	    return modelPinoutCoversSignals(packageSignalShapes, pinout);
	}
	function repeatableBlockFormCompletion(args) {
	    const { modelShape } = args;
	    if (modelShape === null) {
	        return { pinoutComplete: false, requiredParamsSet: false };
	    }
	    return {
	        pinoutComplete: repeatableBlockPinoutComplete({
	            formSignals: args.formSignals,
	            pinout: args.pinout,
	            modelSignals: modelShape.signals,
	        }),
	        requiredParamsSet: requiredParametersSet(modelShape.parameters, args.parameters),
	    };
	}
	function simpleFormCompletion(args) {
	    const { modelShape } = args;
	    if (modelShape === null) {
	        return { pinoutComplete: false, requiredParamsSet: false };
	    }
	    return {
	        pinoutComplete: modelPinoutCoversSignals(modelShape.signals, args.pinout, args.formSignals),
	        requiredParamsSet: requiredParametersSet(modelShape.parameters, args.parameters),
	    };
	}
	
	return formCompletionRules;
}

var directCommitEligibility = {};

var supportedSigmaModelTypes = {};

var hasRequiredSupportedSigmaModelTypes;

function requireSupportedSigmaModelTypes () {
	if (hasRequiredSupportedSigmaModelTypes) return supportedSigmaModelTypes;
	hasRequiredSupportedSigmaModelTypes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AMR_OC_REQUIRED_SIGMA_MODEL_TYPES = exports.SUPPORTED_SIGMA_MODEL_TYPES = void 0;
		exports.isSupportedSigmaModel = isSupportedSigmaModel;
		exports.requiresAmrOcCapture = requiresAmrOcCapture;
		exports.isFormFlowSupportedSigmaModelType = isFormFlowSupportedSigmaModelType;
		const array_submodels_1 = requireArraySubmodels();
		exports.SUPPORTED_SIGMA_MODEL_TYPES = [
		    'capacitor.nonpolarized',
		    'capacitor.polarized',
		    'diode.generic',
		    'diode.zener',
		    'diode.led',
		    'diode.multiled',
		    'diode.tvs.bidirectional',
		    'diode.tvs.unidirectional',
		    'ferrite.standard',
		    'fuse.generic',
		    'fuse.poly',
		    'inductor.coupled',
		    'inductor.standard',
		    'resistor.standard',
		    'resistor.thermistor',
		    'resistor.variable',
		    'resistor.varistor',
		    'mosfet.enhancement.ntype',
		    'mosfet.enhancement.ptype',
		    'transistor.bjt.npn',
		    'transistor.bjt.pnp',
		    'opamp.generic',
		    'passive.array',
		];
		function isSupportedSigmaModel(sigmaModelType) {
		    return exports.SUPPORTED_SIGMA_MODEL_TYPES.includes(sigmaModelType);
		}
		exports.AMR_OC_REQUIRED_SIGMA_MODEL_TYPES = [
		    'mosfet.enhancement.ntype',
		    'mosfet.enhancement.ptype',
		    'transistor.bjt.npn',
		    'transistor.bjt.pnp',
		    'opamp.generic',
		];
		function requiresAmrOcCapture(sigmaModelType) {
		    return exports.AMR_OC_REQUIRED_SIGMA_MODEL_TYPES.includes(sigmaModelType);
		}
		function isFormFlowSupportedSigmaModelType(sigmaModelType, elementSigmaModelType) {
		    if (elementSigmaModelType !== '') {
		        return (0, array_submodels_1.getArrayElementConfig)(elementSigmaModelType) !== null;
		    }
		    return isSupportedSigmaModel(sigmaModelType);
		}
		
	} (supportedSigmaModelTypes));
	return supportedSigmaModelTypes;
}

var hasRequiredDirectCommitEligibility;

function requireDirectCommitEligibility () {
	if (hasRequiredDirectCommitEligibility) return directCommitEligibility;
	hasRequiredDirectCommitEligibility = 1;
	Object.defineProperty(directCommitEligibility, "__esModule", { value: true });
	directCommitEligibility.evaluateDirectCommitEligibility = evaluateDirectCommitEligibility;
	const sigma_engine_shared_1 = requireDist$1();
	const array_submodels_1 = requireArraySubmodels();
	const supported_sigma_model_types_1 = requireSupportedSigmaModelTypes();
	const array_resolution_adapter_1 = requireArrayResolutionAdapter();
	const form_completion_rules_1 = requireFormCompletionRules();
	function withStructuralArraySignalNames(formSignals, spiceModels, elementSignals) {
	    const terminalNameById = new Map(elementSignals.map((signal) => [String(signal.id), signal.name]));
	    const structuralNameByComponentId = new Map();
	    for (const submodel of spiceModels.filter(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel)) {
	        for (const connection of submodel.modelInfo.signalConnectionMap) {
	            const terminalName = terminalNameById.get(String(connection.modelSignalId));
	            if (terminalName !== undefined) {
	                structuralNameByComponentId.set(String(connection.componentSignalId), (0, array_submodels_1.composeArraySignalName)(submodel.subModelId, terminalName));
	            }
	        }
	    }
	    return formSignals.map((signal) => {
	        const structuralName = structuralNameByComponentId.get(String(signal.id));
	        return structuralName === undefined ? signal : { ...signal, name: structuralName };
	    });
	}
	function parametersSetOnEveryElement(spiceModels) {
	    const elements = (spiceModels ?? []).filter(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel);
	    const representative = (0, array_resolution_adapter_1.readArrayElementParametersFromSubModels)(spiceModels);
	    return Object.fromEntries(Object.entries(representative).filter(([name]) => elements.every((element) => element.modelInfo.parameterMap?.[name] !== undefined)));
	}
	function computeCompletionChecks(formOutput, getSigmaModelShape) {
	    const arrayForm = (0, array_resolution_adapter_1.readArrayFormFromResolution)({
	        sigmaModelId: formOutput.sigmaModelType,
	        spiceModels: formOutput.spiceModels,
	    });
	    if (arrayForm !== null) {
	        const inScope = (0, supported_sigma_model_types_1.isFormFlowSupportedSigmaModelType)(formOutput.sigmaModelType, arrayForm.elementModelType);
	        const elementShape = getSigmaModelShape(arrayForm.elementModelType);
	        const { pinoutComplete: pinCountMatchesSignalCount, requiredParamsSet } = (0, form_completion_rules_1.arrayFormCompletion)({
	            elementShape,
	            formSignals: withStructuralArraySignalNames(formOutput.signals, formOutput.spiceModels ?? [], elementShape?.signals ?? []),
	            pinout: formOutput.pinout,
	            parameters: parametersSetOnEveryElement(formOutput.spiceModels),
	            elementCount: arrayForm.elementCount,
	            submodelPrefix: arrayForm.submodelPrefix,
	        });
	        return { inScope, pinCountMatchesSignalCount, requiredParamsSet };
	    }
	    const inScope = (0, supported_sigma_model_types_1.isFormFlowSupportedSigmaModelType)(formOutput.sigmaModelType, '');
	    const modelShape = getSigmaModelShape(formOutput.sigmaModelType);
	    if (modelShape !== null && (modelShape.repeatableBlocks?.length ?? 0) > 0) {
	        const { pinoutComplete: pinCountMatchesSignalCount, requiredParamsSet } = (0, form_completion_rules_1.repeatableBlockFormCompletion)({
	            modelShape,
	            formSignals: formOutput.signals,
	            pinout: formOutput.pinout,
	            parameters: formOutput.parameters,
	        });
	        return { inScope, pinCountMatchesSignalCount, requiredParamsSet };
	    }
	    const { pinoutComplete: pinCountMatchesSignalCount, requiredParamsSet } = (0, form_completion_rules_1.simpleFormCompletion)({
	        modelShape,
	        formSignals: formOutput.signals,
	        pinout: formOutput.pinout,
	        parameters: formOutput.parameters,
	    });
	    return { inScope, pinCountMatchesSignalCount, requiredParamsSet };
	}
	function evaluateDirectCommitEligibility(args) {
	    const { formOutput, getSigmaModelShape } = args;
	    const notBailed = !formOutput.formBailout.bailedOut;
	    if (!notBailed) {
	        return {
	            canCommitDirectly: false,
	            reasons: { inScope: false, pinCountMatchesSignalCount: false, requiredParamsSet: false, notBailed },
	        };
	    }
	    const checks = computeCompletionChecks(formOutput, getSigmaModelShape);
	    const canCommitDirectly = checks.inScope && checks.pinCountMatchesSignalCount && checks.requiredParamsSet && notBailed;
	    return { canCommitDirectly, reasons: { ...checks, notBailed } };
	}
	
	return directCommitEligibility;
}

var payload = {};

var hasRequiredPayload;

function requirePayload () {
	if (hasRequiredPayload) return payload;
	hasRequiredPayload = 1;
	Object.defineProperty(payload, "__esModule", { value: true });
	payload.SigmaModelEntryFormPayloadSchema = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const misc_1 = requireMisc();
	payload.SigmaModelEntryFormPayloadSchema = z.object({
	    sigmaModelType: z.string(),
	    parameters: misc_1.pkgParametersSchema,
	    signals: sigma_engine_shared_1.SignalSchema.array(),
	    pinout: misc_1.pkgPinoutSchema,
	    spiceModels: sigma_engine_shared_1.SPICE.ISubModelOptsSchema.array().default([]),
	});
	
	return payload;
}

var stepDefinition = {};

var hasRequiredStepDefinition;

function requireStepDefinition () {
	if (hasRequiredStepDefinition) return stepDefinition;
	hasRequiredStepDefinition = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SigmaModelEntryFormDefinition = exports.SigmaModelEntryFormOutputSchema = exports.SigmaModelEntryFormInputSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const form_types_1 = requireForm_types();
		const form_schemas_1 = requireFormSchemas();
		const payload_1 = requirePayload();
		exports.SigmaModelEntryFormInputSchema = payload_1.SigmaModelEntryFormPayloadSchema.extend({
		    datasheetFileId: z.string(),
		    manufacturerPartNumber: z.string(),
		});
		exports.SigmaModelEntryFormOutputSchema = payload_1.SigmaModelEntryFormPayloadSchema.extend({
		    sigmaModelType: z.string().min(1, 'sigmaModelType is required'),
		    formBailout: form_types_1.FormBailoutSchema,
		    rawFormData: form_schemas_1.SigmaModelEntryFormDataSchema.optional().catch(undefined),
		});
		exports.SigmaModelEntryFormDefinition = {
		    type: 'SigmaModelEntryForm',
		    displayName: 'Sigma Model Entry Form',
		    inputSchema: exports.SigmaModelEntryFormInputSchema,
		    outputSchema: exports.SigmaModelEntryFormOutputSchema,
		};
		
	} (stepDefinition));
	return stepDefinition;
}

var hasRequiredSigmaModelEntryForm;

function requireSigmaModelEntryForm () {
	if (hasRequiredSigmaModelEntryForm) return sigmaModelEntryForm;
	hasRequiredSigmaModelEntryForm = 1;
	(function (exports) {
		var __createBinding = (sigmaModelEntryForm && sigmaModelEntryForm.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (sigmaModelEntryForm && sigmaModelEntryForm.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.SigmaModelEntryFormDataSchema = exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION = exports.SIGMA_MODEL_ENTRY_FORM_LATEST = void 0;
		var form_schemas_1 = requireFormSchemas();
		Object.defineProperty(exports, "SIGMA_MODEL_ENTRY_FORM_LATEST", { enumerable: true, get: function () { return form_schemas_1.SIGMA_MODEL_ENTRY_FORM_LATEST; } });
		Object.defineProperty(exports, "SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION", { enumerable: true, get: function () { return form_schemas_1.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION; } });
		Object.defineProperty(exports, "SigmaModelEntryFormDataSchema", { enumerable: true, get: function () { return form_schemas_1.SigmaModelEntryFormDataSchema; } });
		__exportStar(requireArrayFormOutput(), exports);
		__exportStar(requireArrayResolutionAdapter(), exports);
		__exportStar(requireFormModelShape(), exports);
		__exportStar(requireModelSignalUniqueness(), exports);
		__exportStar(requireNoConnectSignal(), exports);
		__exportStar(requirePinCountRules(), exports);
		__exportStar(requirePinDesignatorRules(), exports);
		__exportStar(requireFormCompletionRules(), exports);
		__exportStar(requireDirectCommitEligibility(), exports);
		__exportStar(requirePayload(), exports);
		__exportStar(requireFormSchemas(), exports);
		__exportStar(requireStepDefinition(), exports);
		__exportStar(requireSupportedSigmaModelTypes(), exports);
		
	} (sigmaModelEntryForm));
	return sigmaModelEntryForm;
}

var exceptionReview_types = {};

var hasRequiredExceptionReview_types;

function requireExceptionReview_types () {
	if (hasRequiredExceptionReview_types) return exceptionReview_types;
	hasRequiredExceptionReview_types = 1;
	Object.defineProperty(exceptionReview_types, "__esModule", { value: true });
	exceptionReview_types.SlicedReviewItemSchema = exceptionReview_types.ExceptionReviewDecisionSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const consensus_types_1 = requireConsensus_types();
	exceptionReview_types.ExceptionReviewDecisionSchema = z.object({
	    sliceKey: z.string(),
	    anchor: consensus_types_1.AnchorSchema,
	    shouldExist: z.boolean().optional(),
	    value: z.unknown().optional(),
	    isOverride: z.boolean(),
	});
	exceptionReview_types.SlicedReviewItemSchema = z.object({
	    sliceKey: z.string(),
	    item: consensus_types_1.ReviewItemSchema,
	});
	
	return exceptionReview_types;
}

var exceptionReviewSteps = {};

var hasRequiredExceptionReviewSteps;

function requireExceptionReviewSteps () {
	if (hasRequiredExceptionReviewSteps) return exceptionReviewSteps;
	hasRequiredExceptionReviewSteps = 1;
	Object.defineProperty(exceptionReviewSteps, "__esModule", { value: true });
	exceptionReviewSteps.ExceptionReviewStepDefinitions = exceptionReviewSteps.ExceptionReviewPlane = void 0;
	const z = /*@__PURE__*/ requireZod();
	const apply_signal_consolidation_map_1 = requireApplySignalConsolidationMap();
	const apply_mode_consolidation_map_1 = requireApplyModeConsolidationMap();
	const exception_review_types_1 = requireExceptionReview_types();
	var ExceptionReviewPlane;
	(function (ExceptionReviewPlane) {
	    ExceptionReviewPlane["Form"] = "form";
	    ExceptionReviewPlane["General"] = "general";
	})(ExceptionReviewPlane || (exceptionReviewSteps.ExceptionReviewPlane = ExceptionReviewPlane = {}));
	exceptionReviewSteps.ExceptionReviewStepDefinitions = {
	    ExceptionReviewSigmaModel: {
	        type: 'ExceptionReviewSigmaModel',
	        displayName: 'Exception review: sigma model',
	        inputSchema: z.object({
	            consensusRunId: z.string(),
	            packageId: z.string(),
	            proposedSigmaModelType: z.string(),
	            proposedElementSigmaModelType: z.string(),
	            sigmaModelContested: z.boolean(),
	            sigmaModelReviewItems: exception_review_types_1.SlicedReviewItemSchema.array(),
	            mixedCohort: z.boolean().default(false),
	            defaultPlane: z.nativeEnum(ExceptionReviewPlane).default(ExceptionReviewPlane.Form),
	        }),
	        outputSchema: z.object({
	            resolvedSigmaModelType: z.string().min(1, 'resolvedSigmaModelType is required'),
	            resolvedElementSigmaModelType: z.string(),
	            resolvedPlane: z.nativeEnum(ExceptionReviewPlane).default(ExceptionReviewPlane.Form),
	            decisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	    },
	    ExceptionReviewSignalConsolidation: {
	        type: 'ExceptionReviewSignalConsolidation',
	        displayName: 'Exception review: signal consolidation',
	        inputSchema: z.object({
	            consensusRunId: z.string(),
	            packageId: z.string(),
	            resolvedSigmaModelType: z.string(),
	            resolvedElementSigmaModelType: z.string(),
	            precedingDecisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	        outputSchema: z.object({
	            signalMap: apply_signal_consolidation_map_1.SignalConsolidationEntrySchema.array(),
	            decisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	    },
	    ExceptionReviewModeConsolidation: {
	        type: 'ExceptionReviewModeConsolidation',
	        displayName: 'Exception review: modes',
	        inputSchema: z.object({
	            consensusRunId: z.string(),
	            packageId: z.string(),
	            precedingDecisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	        outputSchema: z.object({
	            modeMap: apply_mode_consolidation_map_1.ModeConsolidationEntrySchema.array(),
	            decisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	    },
	    ExceptionReviewValues: {
	        type: 'ExceptionReviewValues',
	        displayName: 'Exception review: values',
	        inputSchema: z.object({
	            consensusRunId: z.string(),
	            packageId: z.string(),
	            sliceKeys: z.string().array(),
	            resolvedSigmaModelType: z.string(),
	            resolvedElementSigmaModelType: z.string(),
	            completesReview: z.boolean(),
	            precedingDecisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	        outputSchema: z.object({
	            decisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	    },
	    ExceptionReviewPublishConfirm: {
	        type: 'ExceptionReviewPublishConfirm',
	        displayName: 'Exception review: confirm',
	        inputSchema: z.object({
	            consensusRunId: z.string(),
	            packageId: z.string(),
	            completesReview: z.boolean(),
	            precedingDecisions: exception_review_types_1.ExceptionReviewDecisionSchema.array(),
	        }),
	        outputSchema: z.object({
	            allowOverwritePublished: z.boolean(),
	        }),
	    },
	};
	
	return exceptionReviewSteps;
}

var adjudication = {};

var adjudicationView_types = {};

var hasRequiredAdjudicationView_types;

function requireAdjudicationView_types () {
	if (hasRequiredAdjudicationView_types) return adjudicationView_types;
	hasRequiredAdjudicationView_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AdjudicationContextSchema = exports.RosterSignalSchema = exports.ContestedValueSchema = exports.AnchorStandingSchema = exports.SerializedAssertionProvenanceSchema = exports.AdjudicationRowSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const adjudication_request_types_1 = requireAdjudicationRequest_types();
		const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
		const json_value_1 = requireJsonValue();
		exports.AdjudicationRowSchema = z.object({
		    rowKey: z.string(),
		    values: z.record(z.string(), json_value_1.JsonValueSchema),
		});
		exports.SerializedAssertionProvenanceSchema = z.object({
		    statementId: z.string(),
		    adjudicatedByUserId: z.string(),
		    adjudicatedAt: z.string(),
		    comment: z.string().nullable(),
		    planId: z.string().nullable(),
		    clauses: package_data_adjudication_types_1.ClauseCitationSchema.array(),
		});
		exports.AnchorStandingSchema = z.object({
		    anchor: consensus_types_1.AnchorSchema,
		    status: package_data_adjudication_types_1.GroundTruthStandingStatusSchema,
		    groundTruthDatum: package_data_adjudication_types_1.AnchoredDatumSchema.nullable(),
		    provenance: exports.SerializedAssertionProvenanceSchema.nullable(),
		});
		exports.ContestedValueSchema = z.object({
		    anchor: consensus_types_1.AnchorSchema,
		    proposed: package_data_adjudication_types_1.AnchoredDatumSchema,
		    sourceKind: adjudication_request_types_1.AdjudicationSourceKindSchema.optional(),
		    sourceRef: z.string().optional(),
		    concern: z.string().optional(),
		});
		exports.RosterSignalSchema = z.object({
		    signalId: z.string(),
		    name: z.string(),
		});
		exports.AdjudicationContextSchema = z.object({
		    packageName: z.string(),
		    manufacturerPartNumber: z.string(),
		    manufacturerName: z.string(),
		    datasheetFileId: z.string().nullable(),
		    packageFamilyId: z.string().nullable().default(null),
		});
		
	} (adjudicationView_types));
	return adjudicationView_types;
}

var adjudicationDiscard_types = {};

var hasRequiredAdjudicationDiscard_types;

function requireAdjudicationDiscard_types () {
	if (hasRequiredAdjudicationDiscard_types) return adjudicationDiscard_types;
	hasRequiredAdjudicationDiscard_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DiscardedRulingSchema = exports.RulingDiscardReasonSchema = exports.RulingDiscardReason = exports.DiscardedRulingKindSchema = exports.DiscardedRulingKind = void 0;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const json_value_1 = requireJsonValue();
		var DiscardedRulingKind;
		(function (DiscardedRulingKind) {
		    DiscardedRulingKind["GroundTruth"] = "ground_truth";
		    DiscardedRulingKind["Error"] = "error";
		    DiscardedRulingKind["Signal"] = "signal";
		})(DiscardedRulingKind || (exports.DiscardedRulingKind = DiscardedRulingKind = {}));
		exports.DiscardedRulingKindSchema = z.nativeEnum(DiscardedRulingKind);
		var RulingDiscardReason;
		(function (RulingDiscardReason) {
		    RulingDiscardReason["RowRemoved"] = "row_removed";
		    RulingDiscardReason["RowNotOnPackage"] = "row_not_on_package";
		    RulingDiscardReason["SignalNotOnPackage"] = "signal_not_on_package";
		    RulingDiscardReason["SignalRemoved"] = "signal_removed";
		    RulingDiscardReason["SignalRenamed"] = "signal_renamed";
		    RulingDiscardReason["SignalNameTaken"] = "signal_name_taken";
		    RulingDiscardReason["SignalGainedPins"] = "signal_gained_pins";
		    RulingDiscardReason["ColumnNotWritable"] = "column_not_writable";
		    RulingDiscardReason["DependsOnDiscarded"] = "depends_on_discarded";
		})(RulingDiscardReason || (exports.RulingDiscardReason = RulingDiscardReason = {}));
		exports.RulingDiscardReasonSchema = z.nativeEnum(RulingDiscardReason);
		exports.DiscardedRulingSchema = z.object({
		    kind: exports.DiscardedRulingKindSchema,
		    anchor: consensus_types_1.AnchorSchema.nullable(),
		    rawValue: json_value_1.JsonValueSchema.nullable(),
		    signalName: z.string().nullable(),
		    reason: exports.RulingDiscardReasonSchema,
		    renamedTo: z.string().nullable(),
		});
		
	} (adjudicationDiscard_types));
	return adjudicationDiscard_types;
}

var adjudicationStep_types = {};

var hasRequiredAdjudicationStep_types;

function requireAdjudicationStep_types () {
	if (hasRequiredAdjudicationStep_types) return adjudicationStep_types;
	hasRequiredAdjudicationStep_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AdjudicationReviewOutputSchema = exports.SignalDecisionDraftSchema = exports.SignalDispositionKind = exports.ErrorDecisionDraftSchema = exports.GroundTruthDecisionDraftSchema = exports.AdjudicationReviewInputSchema = void 0;
		exports.isGroundTruthRuled = isGroundTruthRuled;
		exports.isErrorRuled = isErrorRuled;
		exports.rowsRuledAbsent = rowsRuledAbsent;
		exports.absentRowReasons = absentRowReasons;
		const z = /*@__PURE__*/ requireZod();
		const consensus_types_1 = requireConsensus_types();
		const property_key_1 = requirePropertyKey();
		const slice_key_1 = requireSliceKey();
		const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
		const json_value_1 = requireJsonValue();
		const adjudication_view_types_1 = requireAdjudicationView_types();
		exports.AdjudicationReviewInputSchema = z.object({
		    requestId: z.string(),
		    packageId: z.string(),
		    sliceKey: slice_key_1.SliceKeySchema,
		    sliceVersion: z.number().int(),
		    sigmaModelType: z.string(),
		    propertyKey: property_key_1.PropertyKeySchema,
		    resolutionEntryId: z.string(),
		    context: adjudication_view_types_1.AdjudicationContextSchema,
		    rows: adjudication_view_types_1.AdjudicationRowSchema.array(),
		    standings: adjudication_view_types_1.AnchorStandingSchema.array(),
		    contested: adjudication_view_types_1.ContestedValueSchema.array(),
		    roster: adjudication_view_types_1.RosterSignalSchema.array().default([]),
		});
		exports.GroundTruthDecisionDraftSchema = z.object({
		    anchor: consensus_types_1.AnchorSchema,
		    rawValue: json_value_1.JsonValueSchema.optional(),
		    existence: package_data_adjudication_types_1.RowExistenceSchema.optional(),
		    clauses: package_data_adjudication_types_1.ClauseCitationsSchema.optional(),
		    comment: z.string().nullable().optional(),
		});
		function isGroundTruthRuled(decision) {
		    return decision !== undefined && (decision.rawValue !== undefined || decision.existence !== undefined);
		}
		exports.ErrorDecisionDraftSchema = z.object({
		    anchor: consensus_types_1.AnchorSchema,
		    rawValue: json_value_1.JsonValueSchema.nullable(),
		    clauses: package_data_adjudication_types_1.ClauseCitationsSchema.optional(),
		    errorCodeIds: z.string().array().optional(),
		    comment: z.string().nullable().optional(),
		});
		function isErrorRuled(decision) {
		    return decision !== undefined && (decision.clauses?.length ?? 0) > 0;
		}
		function rowsRuledAbsent(output) {
		    return new Set(output.groundTruthDecisions.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Row && decision.existence === package_data_adjudication_types_1.RowExistence.Absent ?
		        [decision.anchor.rowKey]
		        : []));
		}
		function absentRowReasons(output) {
		    const absent = rowsRuledAbsent(output);
		    const reasons = new Map();
		    for (const decision of output.errorDecisions) {
		        if (decision.anchor.kind === consensus_types_1.AnchorKind.Row && absent.has(decision.anchor.rowKey) && isErrorRuled(decision)) {
		            reasons.set(decision.anchor.rowKey, decision);
		        }
		    }
		    return reasons;
		}
		var SignalDispositionKind;
		(function (SignalDispositionKind) {
		    SignalDispositionKind["Rename"] = "rename";
		    SignalDispositionKind["Create"] = "create";
		    SignalDispositionKind["Delete"] = "delete";
		})(SignalDispositionKind || (exports.SignalDispositionKind = SignalDispositionKind = {}));
		exports.SignalDecisionDraftSchema = z.object({
		    kind: z.nativeEnum(SignalDispositionKind),
		    name: z.string().optional(),
		    signalId: z.string().optional(),
		    clauses: package_data_adjudication_types_1.ClauseCitationsSchema.optional(),
		    errorCodeIds: z.string().array().optional(),
		    comment: z.string().nullable().optional(),
		});
		exports.AdjudicationReviewOutputSchema = z.object({
		    groundTruthDecisions: exports.GroundTruthDecisionDraftSchema.array().default([]),
		    errorDecisions: exports.ErrorDecisionDraftSchema.array().default([]),
		    signalDecisions: exports.SignalDecisionDraftSchema.array().default([]),
		});
		
	} (adjudicationStep_types));
	return adjudicationStep_types;
}

var adjudicationRoster = {};

var adjudicationAdmittedRows = {};

var adjudicationSlice = {};

var hasRequiredAdjudicationSlice;

function requireAdjudicationSlice () {
	if (hasRequiredAdjudicationSlice) return adjudicationSlice;
	hasRequiredAdjudicationSlice = 1;
	Object.defineProperty(adjudicationSlice, "__esModule", { value: true });
	adjudicationSlice.adjudicationSliceOf = adjudicationSliceOf;
	adjudicationSlice.normalizedCellValueOf = normalizedCellValueOf;
	adjudicationSlice.sameCellValue = sameCellValue;
	adjudicationSlice.signalKeyColumnOf = signalKeyColumnOf;
	const consensus_types_1 = requireConsensus_types();
	const general_package_slices_1 = requireGeneralPackageSlices();
	const slice_key_1 = requireSliceKey();
	const SIGNAL_KEY_COLUMN_BY_SLICE = {
	    [slice_key_1.SliceKey.Pinout]: 'signalName',
	    [slice_key_1.SliceKey.SignalAttributes]: 'signalName',
	    [slice_key_1.SliceKey.FloatingBehaviour]: 'signalName',
	};
	const CACHE_KEY_SEPARATOR = '\x1F';
	const sliceCache = new Map();
	function adjudicationSliceOf(input, paramUnitsResolver) {
	    const cacheKey = [input.sliceKey, input.sigmaModelType].join(CACHE_KEY_SEPARATOR);
	    const cached = paramUnitsResolver === undefined ? sliceCache.get(cacheKey) : undefined;
	    if (cached !== undefined) {
	        return cached;
	    }
	    const slice = (0, general_package_slices_1.createGeneralReviewSlices)(paramUnitsResolver, {
	        includeModesSlice: true,
	        sigmaModelType: input.sigmaModelType,
	        includePinAssignmentsSlice: true,
	    }).find((candidate) => candidate.sliceKey === input.sliceKey);
	    if (slice === undefined) {
	        throw new Error(`No general-plane slice defines '${input.sliceKey}', so its values cannot be normalized.`);
	    }
	    if (paramUnitsResolver === undefined) {
	        sliceCache.set(cacheKey, slice);
	    }
	    return slice;
	}
	function normalizedCellValueOf(raw, anchor, input, slice) {
	    if (raw === null || raw === undefined) {
	        return null;
	    }
	    const column = anchor.kind === consensus_types_1.AnchorKind.Field ? anchor.column : '';
	    const row = input.rows.find((candidate) => candidate.rowKey === anchor.rowKey)?.values;
	    const normalized = slice.normalizeValue(column, raw, row);
	    return normalized === '' ? null : normalized;
	}
	function sameCellValue(input, anchor, left, right) {
	    const slice = adjudicationSliceOf(input);
	    return normalizedCellValueOf(left, anchor, input, slice) === normalizedCellValueOf(right, anchor, input, slice);
	}
	function signalKeyColumnOf(sliceKey) {
	    return SIGNAL_KEY_COLUMN_BY_SLICE[sliceKey] ?? null;
	}
	
	return adjudicationSlice;
}

var hasRequiredAdjudicationAdmittedRows;

function requireAdjudicationAdmittedRows () {
	if (hasRequiredAdjudicationAdmittedRows) return adjudicationAdmittedRows;
	hasRequiredAdjudicationAdmittedRows = 1;
	Object.defineProperty(adjudicationAdmittedRows, "__esModule", { value: true });
	adjudicationAdmittedRows.synthesizedRowKeysOf = synthesizedRowKeysOf;
	adjudicationAdmittedRows.admittedRowKeysOf = admittedRowKeysOf;
	adjudicationAdmittedRows.canRecoverRowKey = canRecoverRowKey;
	adjudicationAdmittedRows.recoveredKeyCellsOf = recoveredKeyCellsOf;
	adjudicationAdmittedRows.unheldRowCellsOf = unheldRowCellsOf;
	adjudicationAdmittedRows.admittedRowContentOf = admittedRowContentOf;
	adjudicationAdmittedRows.proposedRowContentOf = proposedRowContentOf;
	const consensus_types_1 = requireConsensus_types();
	const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
	const adjudication_slice_1 = requireAdjudicationSlice();
	const adjudication_step_types_1 = requireAdjudicationStep_types();
	function synthesizedRowKeysOf(input) {
	    const held = new Set(input.rows.map((row) => row.rowKey));
	    const raisedAsRows = new Set(input.contested.flatMap((value) => (value.anchor.kind === consensus_types_1.AnchorKind.Row ? [value.anchor.rowKey] : [])));
	    const keys = new Set();
	    for (const value of input.contested) {
	        const { rowKey } = value.anchor;
	        if (value.anchor.kind === consensus_types_1.AnchorKind.Field && !held.has(rowKey) && !raisedAsRows.has(rowKey)) {
	            keys.add(rowKey);
	        }
	    }
	    return [...keys];
	}
	function admittedRowKeysOf(input, output) {
	    const held = new Set(input.rows.map((row) => row.rowKey));
	    return new Set(output.groundTruthDecisions.flatMap((decision) => (decision.anchor.kind === consensus_types_1.AnchorKind.Row &&
	        decision.existence === package_data_adjudication_types_1.RowExistence.Present &&
	        !held.has(decision.anchor.rowKey)) ?
	        [decision.anchor.rowKey]
	        : []));
	}
	function canRecoverRowKey(sliceKey) {
	    return (0, adjudication_slice_1.signalKeyColumnOf)(sliceKey) !== null;
	}
	function recoveredKeyCellsOf(input, output, rowKey) {
	    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
	    const column = (0, adjudication_slice_1.signalKeyColumnOf)(input.sliceKey);
	    const identifies = (name) => column !== null && slice.rowKeyOf({ [column]: name }) === rowKey;
	    const created = output.signalDecisions.find((decision) => decision.kind === adjudication_step_types_1.SignalDispositionKind.Create && decision.name !== undefined && identifies(decision.name))?.name;
	    const proposed = proposedRowContentOf(input, rowKey);
	    if (proposed !== null) {
	        const cells = Object.fromEntries(slice.keyColumnNames().map((key) => [key, proposed[key] ?? null]));
	        return column === null || created === undefined ? cells : { ...cells, [column]: created };
	    }
	    if (column === null) {
	        return null;
	    }
	    const held = input.roster.find((signal) => identifies(signal.name));
	    return { [column]: created ?? held?.name ?? rowKey };
	}
	function unheldRowCellsOf(input, output, rowKey) {
	    const keyCells = recoveredKeyCellsOf(input, output, rowKey);
	    if (keyCells !== null) {
	        return keyCells;
	    }
	    const decoded = (0, adjudication_slice_1.adjudicationSliceOf)(input).rowCellsFromKey(rowKey);
	    return decoded === null ? null : respelledAsSnapshot(input, decoded);
	}
	function respelledAsSnapshot(input, decoded) {
	    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
	    const cells = input.rows.flatMap((row) => Object.entries(row.values).flatMap(([column, value]) => (typeof value === 'string' ? [{ column, value }] : [])));
	    const fallbacks = [...cells.map((cell) => cell.value), ...input.roster.map((signal) => signal.name)];
	    return Object.fromEntries(Object.entries(decoded).map(([column, normalized]) => {
	        if (normalized === '') {
	            return [column, normalized];
	        }
	        const candidates = [...cells.flatMap((cell) => (cell.column === column ? [cell.value] : [])), ...fallbacks];
	        const spelling = candidates.find((candidate) => slice.normalizeValue(column, candidate, decoded) === normalized);
	        return [column, spelling ?? normalized];
	    }));
	}
	function admittedRowContentOf(input, output, rowKey) {
	    const keyCells = recoveredKeyCellsOf(input, output, rowKey);
	    if (keyCells === null) {
	        return null;
	    }
	    const ruled = Object.fromEntries(output.groundTruthDecisions.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Field && decision.anchor.rowKey === rowKey && (0, adjudication_step_types_1.isGroundTruthRuled)(decision) ?
	        [[decision.anchor.column, decision.rawValue ?? null]]
	        : []));
	    const proposed = proposedRowContentOf(input, rowKey);
	    if (proposed === null && Object.keys(ruled).length === 0) {
	        return null;
	    }
	    return { ...(proposed ?? {}), ...keyCells, ...ruled };
	}
	function proposedRowContentOf(input, rowKey) {
	    const proposed = input.contested.find((value) => value.anchor.kind === consensus_types_1.AnchorKind.Row && value.anchor.rowKey === rowKey)?.proposed;
	    if (proposed === undefined || proposed.kind !== consensus_types_1.AnchorKind.Row) {
	        return null;
	    }
	    const content = proposed.rowContentRaw;
	    return typeof content === 'object' && content !== null && !Array.isArray(content) ? content : null;
	}
	
	return adjudicationAdmittedRows;
}

var hasRequiredAdjudicationRoster;

function requireAdjudicationRoster () {
	if (hasRequiredAdjudicationRoster) return adjudicationRoster;
	hasRequiredAdjudicationRoster = 1;
	Object.defineProperty(adjudicationRoster, "__esModule", { value: true });
	adjudicationRoster.IntroducedSignalOrigin = void 0;
	adjudicationRoster.signalValuedColumnOf = signalValuedColumnOf;
	adjudicationRoster.sliceNeedsRoster = sliceNeedsRoster;
	adjudicationRoster.ruledSignalColumnOf = ruledSignalColumnOf;
	adjudicationRoster.signalIdentityKey = signalIdentityKey;
	adjudicationRoster.rosterByNameKey = rosterByNameKey;
	adjudicationRoster.introducedSignalNames = introducedSignalNames;
	adjudicationRoster.signalDecisionForName = signalDecisionForName;
	adjudicationRoster.pinnedSignalNameKeysAfter = pinnedSignalNameKeysAfter;
	adjudicationRoster.effectiveSignalDecisions = effectiveSignalDecisions;
	adjudicationRoster.rosterAfterDecisions = rosterAfterDecisions;
	adjudicationRoster.isSignalRuled = isSignalRuled;
	const signal_name_canonicalization_1 = requireSignalNameCanonicalization();
	const consensus_types_1 = requireConsensus_types();
	const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
	const slice_key_1 = requireSliceKey();
	const adjudication_step_types_1 = requireAdjudicationStep_types();
	const adjudication_admitted_rows_1 = requireAdjudicationAdmittedRows();
	const adjudication_slice_1 = requireAdjudicationSlice();
	const SIGNAL_VALUED_COLUMN_BY_SLICE = {
	    [slice_key_1.SliceKey.PinAssignments]: 'signalName',
	};
	var IntroducedSignalOrigin;
	(function (IntroducedSignalOrigin) {
	    IntroducedSignalOrigin["RulingValue"] = "ruling_value";
	    IntroducedSignalOrigin["RowKey"] = "row_key";
	})(IntroducedSignalOrigin || (adjudicationRoster.IntroducedSignalOrigin = IntroducedSignalOrigin = {}));
	function signalValuedColumnOf(sliceKey) {
	    return SIGNAL_VALUED_COLUMN_BY_SLICE[sliceKey] ?? null;
	}
	function sliceNeedsRoster(sliceKey) {
	    return signalValuedColumnOf(sliceKey) !== null || (0, adjudication_slice_1.signalKeyColumnOf)(sliceKey) !== null;
	}
	function ruledSignalColumnOf(input) {
	    return input.roster.length === 0 ? null : signalValuedColumnOf(input.sliceKey);
	}
	function signalIdentityKey(name) {
	    return (0, signal_name_canonicalization_1.canonicalizeSignalNameForComparison)(name);
	}
	function rosterByNameKey(roster) {
	    const byKey = new Map();
	    for (const signal of roster) {
	        const key = signalIdentityKey(signal.name);
	        if (!byKey.has(key)) {
	            byKey.set(key, signal);
	        }
	    }
	    return byKey;
	}
	function introducedSignalNames(input, output) {
	    const introduced = new Map();
	    for (const entry of [...valueIntroducedNames(input, output), ...rowKeyIntroducedNames(input, output)]) {
	        const key = signalIdentityKey(entry.name);
	        if (!introduced.has(key)) {
	            introduced.set(key, entry);
	        }
	    }
	    return [...introduced.values()];
	}
	function valueIntroducedNames(input, output) {
	    const column = ruledSignalColumnOf(input);
	    if (column === null) {
	        return [];
	    }
	    const held = rosterByNameKey(input.roster);
	    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
	    const introduced = new Map();
	    for (const decision of output.groundTruthDecisions) {
	        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Field || decision.anchor.column !== column) {
	            continue;
	        }
	        if (!(0, adjudication_step_types_1.isGroundTruthRuled)(decision) || absentRowKeys.has(decision.anchor.rowKey)) {
	            continue;
	        }
	        const name = asName(decision.rawValue);
	        if (name === null) {
	            continue;
	        }
	        const key = signalIdentityKey(name);
	        if (held.has(key) || introduced.has(key)) {
	            continue;
	        }
	        introduced.set(key, { name, anchor: decision.anchor, origin: IntroducedSignalOrigin.RulingValue });
	    }
	    return [...introduced.values()];
	}
	function rowKeyIntroducedNames(input, output) {
	    const column = (0, adjudication_slice_1.signalKeyColumnOf)(input.sliceKey);
	    if (column === null || input.roster.length === 0) {
	        return [];
	    }
	    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
	    const heldRowKeys = new Set(input.roster.map((signal) => slice.rowKeyOf({ [column]: signal.name })));
	    return [...(0, adjudication_admitted_rows_1.admittedRowKeysOf)(input, output)].flatMap((rowKey) => {
	        if (heldRowKeys.has(rowKey)) {
	            return [];
	        }
	        const proposedName = (0, adjudication_admitted_rows_1.proposedRowContentOf)(input, rowKey)?.[column];
	        return [
	            {
	                name: typeof proposedName === 'string' ? proposedName : rowKey,
	                anchor: { kind: consensus_types_1.AnchorKind.Row, rowKey },
	                origin: IntroducedSignalOrigin.RowKey,
	            },
	        ];
	    });
	}
	function signalDecisionForName(output, name) {
	    const key = signalIdentityKey(name);
	    return output.signalDecisions.find((decision) => decision.name !== undefined && signalIdentityKey(decision.name) === key);
	}
	function pinnedSignalNameKeysAfter(input, output) {
	    const column = ruledSignalColumnOf(input);
	    if (column === null) {
	        return new Set();
	    }
	    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
	    const valueByRowKey = new Map();
	    for (const row of input.rows) {
	        valueByRowKey.set(row.rowKey, row.values[column] ?? null);
	    }
	    for (const decision of output.groundTruthDecisions) {
	        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Row || decision.existence !== package_data_adjudication_types_1.RowExistence.Present) {
	            continue;
	        }
	        if (valueByRowKey.has(decision.anchor.rowKey)) {
	            continue;
	        }
	        valueByRowKey.set(decision.anchor.rowKey, (0, adjudication_admitted_rows_1.proposedRowContentOf)(input, decision.anchor.rowKey)?.[column] ?? null);
	    }
	    for (const decision of output.groundTruthDecisions) {
	        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Field || decision.anchor.column !== column) {
	            continue;
	        }
	        if ((0, adjudication_step_types_1.isGroundTruthRuled)(decision)) {
	            valueByRowKey.set(decision.anchor.rowKey, decision.rawValue ?? null);
	        }
	    }
	    const keys = new Set();
	    for (const [rowKey, value] of valueByRowKey) {
	        if (absentRowKeys.has(rowKey)) {
	            continue;
	        }
	        const name = asName(value);
	        if (name !== null) {
	            keys.add(signalIdentityKey(name));
	        }
	    }
	    return keys;
	}
	function effectiveSignalDecisions(input, output) {
	    const introduced = new Set(introducedSignalNames(input, output).map((entry) => signalIdentityKey(entry.name)));
	    const heldIds = new Set(input.roster.map((signal) => signal.signalId));
	    return output.signalDecisions.filter((decision) => {
	        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Delete) {
	            return decision.signalId !== undefined && heldIds.has(decision.signalId);
	        }
	        return decision.name !== undefined && introduced.has(signalIdentityKey(decision.name));
	    });
	}
	function rosterAfterDecisions(roster, decisions) {
	    const renamedById = new Map();
	    const deletedIds = new Set();
	    const created = [];
	    for (const decision of decisions) {
	        if (!isSignalRuled(decision)) {
	            continue;
	        }
	        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Rename && decision.signalId !== undefined) {
	            renamedById.set(decision.signalId, decision.name ?? '');
	            continue;
	        }
	        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Delete && decision.signalId !== undefined) {
	            deletedIds.add(decision.signalId);
	            continue;
	        }
	        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Create && decision.name !== undefined) {
	            created.push({ signalId: '', name: decision.name });
	        }
	    }
	    const surviving = roster.flatMap((signal) => deletedIds.has(signal.signalId) ? [] : [{ ...signal, name: renamedById.get(signal.signalId) ?? signal.name }]);
	    return [...surviving, ...created];
	}
	function isSignalRuled(decision) {
	    if (decision === undefined) {
	        return false;
	    }
	    switch (decision.kind) {
	        case adjudication_step_types_1.SignalDispositionKind.Rename:
	            return isNonEmpty(decision.name) && isNonEmpty(decision.signalId) && (decision.clauses?.length ?? 0) > 0;
	        case adjudication_step_types_1.SignalDispositionKind.Create:
	            return isNonEmpty(decision.name);
	        case adjudication_step_types_1.SignalDispositionKind.Delete:
	            return isNonEmpty(decision.signalId);
	    }
	}
	function isNonEmpty(value) {
	    return value !== undefined && value !== '';
	}
	function asName(value) {
	    return typeof value === 'string' && value !== '' ? value : null;
	}
	
	return adjudicationRoster;
}

var adjudicationQuestions = {};

var hasRequiredAdjudicationQuestions;

function requireAdjudicationQuestions () {
	if (hasRequiredAdjudicationQuestions) return adjudicationQuestions;
	hasRequiredAdjudicationQuestions = 1;
	Object.defineProperty(adjudicationQuestions, "__esModule", { value: true });
	adjudicationQuestions.AdjudicationQuestionStatus = adjudicationQuestions.AdjudicationQuestionKind = void 0;
	adjudicationQuestions.questionKeyOf = questionKeyOf;
	adjudicationQuestions.isGroundTruthQuestion = isGroundTruthQuestion;
	adjudicationQuestions.isErrorQuestion = isErrorQuestion;
	adjudicationQuestions.supersededGroundTruthOf = supersededGroundTruthOf;
	adjudicationQuestions.signalQuestionKeyOf = signalQuestionKeyOf;
	adjudicationQuestions.buildAdjudicationQuestions = buildAdjudicationQuestions;
	adjudicationQuestions.isAdjudicationComplete = isAdjudicationComplete;
	const consensus_types_1 = requireConsensus_types();
	const signal_name_normalization_1 = requireSignalNameNormalization();
	const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
	const adjudication_roster_1 = requireAdjudicationRoster();
	const adjudication_step_types_1 = requireAdjudicationStep_types();
	const adjudication_admitted_rows_1 = requireAdjudicationAdmittedRows();
	const adjudication_slice_1 = requireAdjudicationSlice();
	const KEY_SEPARATOR = '\x1F';
	var AdjudicationQuestionKind;
	(function (AdjudicationQuestionKind) {
	    AdjudicationQuestionKind["GroundTruthExistence"] = "ground_truth_existence";
	    AdjudicationQuestionKind["GroundTruthValue"] = "ground_truth_value";
	    AdjudicationQuestionKind["InferenceError"] = "inference_error";
	    AdjudicationQuestionKind["SignalDisposition"] = "signal_disposition";
	    AdjudicationQuestionKind["SupersededGroundTruth"] = "superseded_ground_truth";
	})(AdjudicationQuestionKind || (adjudicationQuestions.AdjudicationQuestionKind = AdjudicationQuestionKind = {}));
	var AdjudicationQuestionStatus;
	(function (AdjudicationQuestionStatus) {
	    AdjudicationQuestionStatus["Open"] = "open";
	    AdjudicationQuestionStatus["Blocked"] = "blocked";
	    AdjudicationQuestionStatus["Answered"] = "answered";
	    AdjudicationQuestionStatus["Moot"] = "moot";
	})(AdjudicationQuestionStatus || (adjudicationQuestions.AdjudicationQuestionStatus = AdjudicationQuestionStatus = {}));
	function questionKeyOf(kind, anchor, rawValue) {
	    const parts = [kind, (0, consensus_types_1.anchorKey)(anchor)];
	    if (kind === AdjudicationQuestionKind.InferenceError || kind === AdjudicationQuestionKind.SupersededGroundTruth) {
	        parts.push(canonicalJson(rawValue ?? null));
	    }
	    return parts.join(KEY_SEPARATOR);
	}
	function isGroundTruthQuestion(question) {
	    return (question.kind === AdjudicationQuestionKind.GroundTruthValue ||
	        question.kind === AdjudicationQuestionKind.GroundTruthExistence);
	}
	function isErrorQuestion(question) {
	    return (question.kind === AdjudicationQuestionKind.InferenceError ||
	        question.kind === AdjudicationQuestionKind.SupersededGroundTruth);
	}
	function supersededGroundTruthOf(input, standing, decision) {
	    const previous = standing?.groundTruthDatum ?? null;
	    if (previous === null || !(0, adjudication_step_types_1.isGroundTruthRuled)(decision)) {
	        return null;
	    }
	    if (decision.anchor.kind === consensus_types_1.AnchorKind.Field && previous.kind === consensus_types_1.AnchorKind.Field) {
	        return (0, adjudication_slice_1.sameCellValue)(input, decision.anchor, decision.rawValue, previous.value.raw) ? null : ({ rawValue: previous.value.raw });
	    }
	    if (decision.anchor.kind === consensus_types_1.AnchorKind.Row &&
	        previous.kind === consensus_types_1.AnchorKind.Row &&
	        previous.existence === package_data_adjudication_types_1.RowExistence.Present &&
	        decision.existence === package_data_adjudication_types_1.RowExistence.Absent) {
	        return { rawValue: null };
	    }
	    return null;
	}
	function signalQuestionKeyOf(signalName) {
	    return [AdjudicationQuestionKind.SignalDisposition, (0, adjudication_roster_1.signalIdentityKey)(signalName)].join(KEY_SEPARATOR);
	}
	function buildAdjudicationQuestions(input, output) {
	    const standingByAnchor = new Map(input.standings.map((standing) => [(0, consensus_types_1.anchorKey)(standing.anchor), standing]));
	    const rowByKey = new Map(input.rows.map((row) => [row.rowKey, row]));
	    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
	    const synthesizedRowKeys = new Set((0, adjudication_admitted_rows_1.synthesizedRowKeysOf)(input));
	    const rowQuestions = new Map();
	    const questions = [];
	    for (const [key, contested] of groupContestedByAnchor(input.contested)) {
	        const standing = standingByAnchor.get(key) ?? null;
	        if (standing?.status === package_data_adjudication_types_1.GroundTruthStandingStatus.RowRuledAbsent) {
	            continue;
	        }
	        const anchor = contested[0].anchor;
	        let blockedBy;
	        if (synthesizedRowKeys.has(anchor.rowKey)) {
	            if (!rowQuestions.has(anchor.rowKey)) {
	                const rowQuestion = buildSynthesizedRowQuestion(input, output, anchor.rowKey, standingByAnchor);
	                rowQuestions.set(anchor.rowKey, rowQuestion);
	                questions.push(...rowQuestionsOf(input, rowQuestion, standingByAnchor, output));
	            }
	            const rowQuestion = rowQuestions.get(anchor.rowKey) ?? null;
	            if (rowQuestion !== null && !isRuledPresent(output, anchor.rowKey)) {
	                blockedBy = rowQuestion.questionKey;
	            }
	        }
	        const isInAbsentRow = anchor.kind === consensus_types_1.AnchorKind.Field && absentRowKeys.has(anchor.rowKey);
	        const groundTruthQuestion = buildGroundTruthQuestion(anchor, standing, contested, rowByKey, output, isInAbsentRow, blockedBy);
	        if (groundTruthQuestion !== null) {
	            questions.push(groundTruthQuestion);
	        }
	        for (const value of contested) {
	            questions.push(isInAbsentRow ?
	                { ...inferenceQuestionBase(value, groundTruthQuestion), status: AdjudicationQuestionStatus.Moot }
	                : buildInferenceQuestion(input, value, standing, groundTruthQuestion, output));
	        }
	        const superseded = isInAbsentRow ? null : buildSupersededQuestion(input, anchor, standing, contested, output);
	        if (superseded !== null) {
	            questions.push(superseded);
	        }
	    }
	    return [
	        ...questions,
	        ...buildAdmittedRowValueQuestions(input, output, standingByAnchor),
	        ...buildSignalDispositionQuestions(input, output),
	    ];
	}
	function rowQuestionsOf(input, rowQuestion, standingByAnchor, output) {
	    if (rowQuestion === null) {
	        return [];
	    }
	    const superseded = buildSupersededQuestion(input, rowQuestion.anchor, standingByAnchor.get((0, consensus_types_1.anchorKey)(rowQuestion.anchor)) ?? null, [], output);
	    return superseded === null ? [rowQuestion] : [rowQuestion, superseded];
	}
	function buildSynthesizedRowQuestion(input, output, rowKey, standingByAnchor) {
	    const anchor = { kind: consensus_types_1.AnchorKind.Row, rowKey };
	    const standing = standingByAnchor.get((0, consensus_types_1.anchorKey)(anchor)) ?? null;
	    if (standing?.status === package_data_adjudication_types_1.GroundTruthStandingStatus.VettedAndCurrent) {
	        return null;
	    }
	    const proposedCells = Object.fromEntries(input.contested.flatMap((value) => (value.anchor.kind === consensus_types_1.AnchorKind.Field &&
	        value.anchor.rowKey === rowKey &&
	        value.proposed.kind === consensus_types_1.AnchorKind.Field) ?
	        [[value.anchor.column, value.proposed.value.raw]]
	        : []));
	    return {
	        kind: AdjudicationQuestionKind.GroundTruthExistence,
	        questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthExistence, anchor),
	        anchor,
	        status: (0, adjudication_step_types_1.isGroundTruthRuled)(findGroundTruthDecision(output, anchor)) ?
	            AdjudicationQuestionStatus.Answered
	            : AdjudicationQuestionStatus.Open,
	        currentExistence: package_data_adjudication_types_1.RowExistence.Absent,
	        rowContent: { ...proposedCells, ...((0, adjudication_admitted_rows_1.recoveredKeyCellsOf)(input, output, rowKey) ?? {}) },
	        standing,
	        direction: package_data_adjudication_types_1.DiscrepancyDirection.Extra,
	    };
	}
	function buildAdmittedRowValueQuestions(input, output, standingByAnchor) {
	    const admitted = (0, adjudication_admitted_rows_1.admittedRowKeysOf)(input, output);
	    if (admitted.size === 0) {
	        return [];
	    }
	    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
	    const contestedKeys = new Set(input.contested.map((value) => (0, consensus_types_1.anchorKey)(value.anchor)));
	    return [...admitted].flatMap((rowKey) => {
	        const proposed = (0, adjudication_admitted_rows_1.proposedRowContentOf)(input, rowKey);
	        return slice.valueColumnNames().flatMap((column) => {
	            const anchor = { kind: consensus_types_1.AnchorKind.Field, rowKey, column };
	            if (contestedKeys.has((0, consensus_types_1.anchorKey)(anchor))) {
	                return [];
	            }
	            const standing = standingByAnchor.get((0, consensus_types_1.anchorKey)(anchor)) ?? null;
	            const proposedValue = proposed?.[column];
	            const question = {
	                kind: AdjudicationQuestionKind.GroundTruthValue,
	                questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthValue, anchor),
	                anchor,
	                status: (0, adjudication_step_types_1.isGroundTruthRuled)(findGroundTruthDecision(output, anchor)) ?
	                    AdjudicationQuestionStatus.Answered
	                    : AdjudicationQuestionStatus.Open,
	                currentValue: null,
	                standing,
	                suggestedValues: proposedValue === undefined || proposedValue === null ?
	                    []
	                    : [{ raw: proposedValue, normalized: slice.normalizeValue(column, proposedValue, proposed ?? undefined) }],
	            };
	            const superseded = buildSupersededQuestion(input, anchor, standing, [], output);
	            return superseded === null ? [question] : [question, superseded];
	        });
	    });
	}
	function isRuledPresent(output, rowKey) {
	    return findGroundTruthDecision(output, { kind: consensus_types_1.AnchorKind.Row, rowKey })?.existence === package_data_adjudication_types_1.RowExistence.Present;
	}
	function isAdjudicationComplete(questions) {
	    return questions.every((question) => question.status === AdjudicationQuestionStatus.Answered || question.status === AdjudicationQuestionStatus.Moot);
	}
	function buildSignalDispositionQuestions(input, output) {
	    return (0, adjudication_roster_1.introducedSignalNames)(input, output).map((introduced) => ({
	        kind: AdjudicationQuestionKind.SignalDisposition,
	        questionKey: signalQuestionKeyOf(introduced.name),
	        anchor: introduced.anchor,
	        status: (0, adjudication_roster_1.isSignalRuled)((0, adjudication_roster_1.signalDecisionForName)(output, introduced.name)) ?
	            AdjudicationQuestionStatus.Answered
	            : AdjudicationQuestionStatus.Open,
	        signalName: introduced.name,
	        renameCandidate: introduced.origin === adjudication_roster_1.IntroducedSignalOrigin.RowKey ? null : renameCandidateFor(input.roster, introduced.name),
	        origin: introduced.origin,
	    }));
	}
	function renameCandidateFor(roster, name) {
	    const folded = (0, signal_name_normalization_1.foldSignalNameNotation)(name);
	    if (folded === '') {
	        return null;
	    }
	    const matches = roster.filter((signal) => (0, signal_name_normalization_1.foldSignalNameNotation)(signal.name) === folded);
	    return matches.length === 1 ? matches[0] : null;
	}
	function groupContestedByAnchor(contested) {
	    const grouped = new Map();
	    for (const value of contested) {
	        const key = (0, consensus_types_1.anchorKey)(value.anchor);
	        const existing = grouped.get(key);
	        if (existing === undefined) {
	            grouped.set(key, [value]);
	            continue;
	        }
	        existing.push(value);
	    }
	    return grouped;
	}
	function buildGroundTruthQuestion(anchor, standing, contested, rowByKey, output, isInAbsentRow, blockedBy) {
	    if (standing?.status === package_data_adjudication_types_1.GroundTruthStandingStatus.VettedAndCurrent) {
	        return null;
	    }
	    const decision = findGroundTruthDecision(output, anchor);
	    const status = isInAbsentRow ? AdjudicationQuestionStatus.Moot
	        : blockedBy !== undefined ? AdjudicationQuestionStatus.Blocked
	            : (0, adjudication_step_types_1.isGroundTruthRuled)(decision) ? AdjudicationQuestionStatus.Answered
	                : AdjudicationQuestionStatus.Open;
	    if (anchor.kind === consensus_types_1.AnchorKind.Row) {
	        const row = rowByKey.get(anchor.rowKey);
	        const proposedContent = contested.find((value) => value.proposed.kind === consensus_types_1.AnchorKind.Row)?.proposed;
	        return {
	            kind: AdjudicationQuestionKind.GroundTruthExistence,
	            questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthExistence, anchor),
	            anchor,
	            status,
	            currentExistence: row === undefined ? package_data_adjudication_types_1.RowExistence.Absent : package_data_adjudication_types_1.RowExistence.Present,
	            rowContent: row?.values ??
	                (proposedContent !== undefined && proposedContent.kind === consensus_types_1.AnchorKind.Row ?
	                    asRecord(proposedContent.rowContentRaw)
	                    : {}),
	            standing,
	            direction: row === undefined ? package_data_adjudication_types_1.DiscrepancyDirection.Extra : package_data_adjudication_types_1.DiscrepancyDirection.Missing,
	        };
	    }
	    return {
	        kind: AdjudicationQuestionKind.GroundTruthValue,
	        questionKey: questionKeyOf(AdjudicationQuestionKind.GroundTruthValue, anchor),
	        anchor,
	        status,
	        currentValue: rowByKey.get(anchor.rowKey)?.values[anchor.column] ?? null,
	        standing,
	        suggestedValues: distinctSuggestedValues(contested),
	        ...(blockedBy === undefined ? {} : { blockedBy }),
	    };
	}
	function buildInferenceQuestion(input, contested, standing, groundTruthQuestion, output) {
	    const { anchor } = contested;
	    const base = inferenceQuestionBase(contested, groundTruthQuestion);
	    const { rawValue } = base;
	    if (groundTruthQuestion !== null && groundTruthQuestion.status !== AdjudicationQuestionStatus.Answered) {
	        return { ...base, status: AdjudicationQuestionStatus.Blocked };
	    }
	    if (settledGroundTruthMatches(input, contested, standing, findGroundTruthDecision(output, anchor))) {
	        return { ...base, status: AdjudicationQuestionStatus.Moot };
	    }
	    const answered = (0, adjudication_step_types_1.isErrorRuled)(findErrorDecision(output, anchor, rawValue));
	    return { ...base, status: answered ? AdjudicationQuestionStatus.Answered : AdjudicationQuestionStatus.Open };
	}
	function buildSupersededQuestion(input, anchor, standing, contested, output) {
	    const superseded = supersededGroundTruthOf(input, standing, findGroundTruthDecision(output, anchor));
	    if (superseded === null) {
	        return null;
	    }
	    const asked = contested.some((value) => value.proposed.kind === consensus_types_1.AnchorKind.Row ?
	        superseded.rawValue === null
	        : (0, adjudication_slice_1.sameCellValue)(input, anchor, value.proposed.value.raw, superseded.rawValue));
	    if (asked) {
	        return null;
	    }
	    return {
	        kind: AdjudicationQuestionKind.SupersededGroundTruth,
	        questionKey: questionKeyOf(AdjudicationQuestionKind.SupersededGroundTruth, anchor, superseded.rawValue),
	        anchor,
	        status: (0, adjudication_step_types_1.isErrorRuled)(findErrorDecision(output, anchor, superseded.rawValue)) ?
	            AdjudicationQuestionStatus.Answered
	            : AdjudicationQuestionStatus.Open,
	        rawValue: superseded.rawValue,
	        provenance: standing?.provenance ?? null,
	    };
	}
	function inferenceQuestionBase(contested, groundTruthQuestion) {
	    const { anchor, proposed } = contested;
	    const isRow = proposed.kind === consensus_types_1.AnchorKind.Row;
	    const rawValue = isRow ? null : proposed.value.raw;
	    return {
	        kind: AdjudicationQuestionKind.InferenceError,
	        questionKey: questionKeyOf(AdjudicationQuestionKind.InferenceError, anchor, rawValue),
	        anchor,
	        rawValue,
	        proposedExistence: isRow ? proposed.existence : undefined,
	        sources: [{ sourceKind: contested.sourceKind, sourceRef: contested.sourceRef }],
	        blockedBy: groundTruthQuestion?.questionKey ?? null,
	    };
	}
	function settledGroundTruthMatches(input, contested, standing, decision) {
	    const { proposed } = contested;
	    if (proposed.kind === consensus_types_1.AnchorKind.Row) {
	        const settled = decision?.existence ??
	            (standing?.groundTruthDatum?.kind === consensus_types_1.AnchorKind.Row ? standing.groundTruthDatum.existence : undefined);
	        return settled !== undefined && settled === proposed.existence;
	    }
	    if ((0, adjudication_step_types_1.isGroundTruthRuled)(decision)) {
	        return (0, adjudication_slice_1.sameCellValue)(input, contested.anchor, decision.rawValue, proposed.value.raw);
	    }
	    if (standing?.groundTruthDatum?.kind === consensus_types_1.AnchorKind.Field) {
	        return standing.groundTruthDatum.value.normalized === proposed.value.normalized;
	    }
	    return false;
	}
	function distinctSuggestedValues(contested) {
	    const seen = new Set();
	    const values = [];
	    for (const value of contested) {
	        if (value.proposed.kind !== consensus_types_1.AnchorKind.Field) {
	            continue;
	        }
	        const key = value.proposed.value.normalized ?? canonicalJson(value.proposed.value.raw);
	        if (seen.has(key)) {
	            continue;
	        }
	        seen.add(key);
	        values.push(value.proposed.value);
	    }
	    return values;
	}
	function findGroundTruthDecision(output, anchor) {
	    const key = (0, consensus_types_1.anchorKey)(anchor);
	    return output.groundTruthDecisions.find((decision) => (0, consensus_types_1.anchorKey)(decision.anchor) === key);
	}
	function findErrorDecision(output, anchor, rawValue) {
	    const key = (0, consensus_types_1.anchorKey)(anchor);
	    const value = canonicalJson(rawValue);
	    return output.errorDecisions.find((decision) => (0, consensus_types_1.anchorKey)(decision.anchor) === key && canonicalJson(decision.rawValue) === value);
	}
	function asRecord(value) {
	    return typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
	}
	function canonicalJson(value) {
	    if (value === undefined) {
	        return 'undefined';
	    }
	    return JSON.stringify(value, (_key, nested) => {
	        if (typeof nested !== 'object' || nested === null || Array.isArray(nested)) {
	            return nested;
	        }
	        const entries = Object.entries(nested).sort(([left], [right]) => left < right ? -1
	            : left > right ? 1
	                : 0);
	        return Object.fromEntries(entries);
	    });
	}
	
	return adjudicationQuestions;
}

var adjudicationEncode = {};

var hasRequiredAdjudicationEncode;

function requireAdjudicationEncode () {
	if (hasRequiredAdjudicationEncode) return adjudicationEncode;
	hasRequiredAdjudicationEncode = 1;
	Object.defineProperty(adjudicationEncode, "__esModule", { value: true });
	adjudicationEncode.AdjudicatedRowAdditionSchema = void 0;
	adjudicationEncode.encodeAdjudicationLedgerWrites = encodeAdjudicationLedgerWrites;
	adjudicationEncode.toResolutionDecisions = toResolutionDecisions;
	adjudicationEncode.toAddedRows = toAddedRows;
	const z = /*@__PURE__*/ requireZod();
	const consensus_types_1 = requireConsensus_types();
	const package_data_adjudication_types_1 = requirePackageDataAdjudication_types();
	const slice_key_1 = requireSliceKey();
	const json_value_1 = requireJsonValue();
	const adjudication_admitted_rows_1 = requireAdjudicationAdmittedRows();
	const adjudication_slice_1 = requireAdjudicationSlice();
	const adjudication_roster_1 = requireAdjudicationRoster();
	const adjudication_step_types_1 = requireAdjudicationStep_types();
	function encodeAdjudicationLedgerWrites(input, output, attribution, options = {}) {
	    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input, options.paramUnitsResolver);
	    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
	    const applicable = output.groundTruthDecisions.filter((decision) => (0, adjudication_step_types_1.isGroundTruthRuled)(decision) && !isSuppressed(decision.anchor, absentRowKeys));
	    const settledExistences = settledRowExistences(input, output);
	    const cascadedRenames = cascadedRenameDecisions(input, output, options.excludedRowKeys ?? new Set());
	    const ruledCells = [...applicable, ...cascadedRenames.groundTruth];
	    const assertions = ruledCells.map((decision) => ({
	        address: addressOf(input, decision.anchor),
	        datum: datumOf(decision, input, output, slice),
	        clauses: decision.clauses ?? [],
	        comment: decision.comment ?? null,
	        attribution,
	    }));
	    const ruledWrong = withoutRedundantErrors([
	        ...output.errorDecisions.filter(adjudication_step_types_1.isErrorRuled),
	        ...cascadedErrorDecisions(input, output, slice),
	        ...cascadedRenames.errors,
	    ], assertions, input, slice);
	    const errorStatements = ruledWrong.map((decision) => ({
	        address: addressOf(input, decision.anchor),
	        datum: errorDatumOf(decision.anchor, decision.rawValue, input, output, slice, settledExistences),
	        clauses: decision.clauses ?? [],
	        errorCodeIds: decision.errorCodeIds ?? [],
	        comment: decision.comment ?? null,
	        attribution,
	    }));
	    return {
	        assertions: [...assertions, ...impliedRowAssertions(input, output, ruledCells, attribution)],
	        errorStatements,
	    };
	}
	function withoutRedundantErrors(decisions, assertions, input, slice) {
	    const taken = new Set(assertions.flatMap((assertion) => assertion.datum.kind === consensus_types_1.AnchorKind.Field ?
	        [cellKey(assertion.address.anchor, assertion.datum.value.normalized)]
	        : []));
	    return decisions.filter((decision) => {
	        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Field) {
	            return true;
	        }
	        const key = cellKey(decision.anchor, (0, adjudication_slice_1.normalizedCellValueOf)(decision.rawValue, decision.anchor, input, slice));
	        if (taken.has(key)) {
	            return false;
	        }
	        taken.add(key);
	        return true;
	    });
	}
	function toResolutionDecisions(input, output) {
	    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
	    return output.groundTruthDecisions
	        .filter((decision) => (0, adjudication_step_types_1.isGroundTruthRuled)(decision) && !isSuppressed(decision.anchor, absentRowKeys))
	        .map((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Row ?
	        {
	            sliceKey: input.sliceKey,
	            anchor: decision.anchor,
	            shouldExist: decision.existence === package_data_adjudication_types_1.RowExistence.Present,
	            isOverride: true,
	        }
	        : {
	            sliceKey: input.sliceKey,
	            anchor: decision.anchor,
	            value: decision.rawValue,
	            isOverride: true,
	        });
	}
	adjudicationEncode.AdjudicatedRowAdditionSchema = z.object({
	    sliceKey: slice_key_1.SliceKeySchema,
	    rowKey: z.string(),
	    values: z.record(z.string(), json_value_1.JsonValueSchema),
	});
	function toAddedRows(input, output) {
	    const projectedRowKeys = new Set(input.rows.map((row) => row.rowKey));
	    const admitted = output.groundTruthDecisions.filter((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Row &&
	        decision.existence === package_data_adjudication_types_1.RowExistence.Present &&
	        !projectedRowKeys.has(decision.anchor.rowKey));
	    return admitted
	        .flatMap((decision) => {
	        const content = rowContentOf(input, output, decision.anchor.rowKey);
	        return isRowContent(content) ? [{ rowKey: decision.anchor.rowKey, values: content }] : [];
	    })
	        .sort((left, right) => left.rowKey < right.rowKey ? -1
	        : left.rowKey > right.rowKey ? 1
	            : 0)
	        .map(({ rowKey, values }) => ({ sliceKey: input.sliceKey, rowKey, values }));
	}
	function isRowContent(content) {
	    return typeof content === 'object' && content !== null && !Array.isArray(content);
	}
	function cascadedErrorDecisions(input, output, slice) {
	    const reasons = (0, adjudication_step_types_1.absentRowReasons)(output);
	    if (reasons.size === 0) {
	        return [];
	    }
	    const alreadyRuled = new Set(output.errorDecisions.map((decision) => cellKey(decision.anchor, (0, adjudication_slice_1.normalizedCellValueOf)(decision.rawValue, decision.anchor, input, slice))));
	    return [...reasons.keys()].sort().flatMap((rowKey) => {
	        const reason = reasons.get(rowKey);
	        if (reason === undefined) {
	            return [];
	        }
	        return condemnedValues(input, rowKey, slice)
	            .filter((cell) => !alreadyRuled.has(cellKey({ kind: consensus_types_1.AnchorKind.Field, rowKey, column: cell.column }, cell.normalized)))
	            .map((cell) => ({
	            anchor: { kind: consensus_types_1.AnchorKind.Field, rowKey, column: cell.column },
	            rawValue: cell.rawValue,
	            clauses: reason.clauses,
	            errorCodeIds: reason.errorCodeIds,
	            comment: reason.comment,
	        }));
	    });
	}
	function cascadedRenameDecisions(input, output, excludedRowKeys) {
	    const column = (0, adjudication_roster_1.ruledSignalColumnOf)(input);
	    const renames = (0, adjudication_roster_1.effectiveSignalDecisions)(input, output).filter((decision) => decision.kind === adjudication_step_types_1.SignalDispositionKind.Rename && (0, adjudication_roster_1.isSignalRuled)(decision));
	    if (column === null || renames.length === 0) {
	        return { groundTruth: [], errors: [] };
	    }
	    const nameById = new Map(input.roster.map((signal) => [signal.signalId, signal.name]));
	    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
	    const directlyRuled = new Set(output.groundTruthDecisions.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Field && decision.anchor.column === column ? [decision.anchor.rowKey] : []));
	    const cascaded = { groundTruth: [], errors: [] };
	    for (const rename of renames) {
	        const previousName = rename.signalId === undefined ? undefined : nameById.get(rename.signalId);
	        if (previousName === undefined || rename.name === undefined) {
	            continue;
	        }
	        const previousKey = (0, adjudication_roster_1.signalIdentityKey)(previousName);
	        for (const row of [...input.rows].sort((left, right) => compareRowKeys(left.rowKey, right.rowKey))) {
	            const held = row.values[column];
	            if (typeof held !== 'string' || (0, adjudication_roster_1.signalIdentityKey)(held) !== previousKey) {
	                continue;
	            }
	            if (absentRowKeys.has(row.rowKey) || directlyRuled.has(row.rowKey) || excludedRowKeys.has(row.rowKey)) {
	                continue;
	            }
	            const anchor = { kind: consensus_types_1.AnchorKind.Field, rowKey: row.rowKey, column };
	            cascaded.groundTruth.push({ anchor, rawValue: rename.name, clauses: [], comment: null });
	            cascaded.errors.push({
	                anchor,
	                rawValue: held,
	                clauses: rename.clauses ?? [],
	                errorCodeIds: rename.errorCodeIds ?? [],
	                comment: rename.comment ?? null,
	            });
	        }
	    }
	    return cascaded;
	}
	function compareRowKeys(left, right) {
	    return (left < right ? -1
	        : left > right ? 1
	            : 0);
	}
	function condemnedValues(input, rowKey, slice) {
	    const keyColumns = new Set(slice.keyColumnNames());
	    const held = Object.entries(input.rows.find((row) => row.rowKey === rowKey)?.values ?? {}).map(([column, rawValue]) => ({ column, rawValue }));
	    const proposed = input.contested.flatMap((value) => (value.anchor.kind === consensus_types_1.AnchorKind.Field &&
	        value.anchor.rowKey === rowKey &&
	        value.proposed.kind === consensus_types_1.AnchorKind.Field) ?
	        [{ column: value.anchor.column, rawValue: value.proposed.value.raw }]
	        : []);
	    const byKey = new Map();
	    for (const cell of [...held, ...proposed]) {
	        if (keyColumns.has(cell.column)) {
	            continue;
	        }
	        const normalized = (0, adjudication_slice_1.normalizedCellValueOf)(cell.rawValue, { kind: consensus_types_1.AnchorKind.Field, rowKey, column: cell.column }, input, slice);
	        const key = `${cell.column}\x1F${JSON.stringify(normalized)}`;
	        if (!byKey.has(key)) {
	            byKey.set(key, { ...cell, normalized });
	        }
	    }
	    return [...byKey.entries()]
	        .sort(([left], [right]) => left < right ? -1
	        : left > right ? 1
	            : 0)
	        .map(([, cell]) => cell);
	}
	function cellKey(anchor, normalized) {
	    return `${(0, consensus_types_1.anchorKey)(anchor)}\x1F${JSON.stringify(normalized)}`;
	}
	function impliedRowAssertions(input, output, ruled, attribution) {
	    const explicitRows = new Set(ruled.flatMap((decision) => (decision.anchor.kind === consensus_types_1.AnchorKind.Row ? [decision.anchor.rowKey] : [])));
	    const impliedRows = new Set(ruled.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Field && !explicitRows.has(decision.anchor.rowKey) ?
	        [decision.anchor.rowKey]
	        : []));
	    return [...impliedRows].sort().map((rowKey) => ({
	        address: addressOf(input, { kind: consensus_types_1.AnchorKind.Row, rowKey }),
	        datum: {
	            kind: consensus_types_1.AnchorKind.Row,
	            existence: package_data_adjudication_types_1.RowExistence.Present,
	            rowContentRaw: rowContentOf(input, output, rowKey),
	        },
	        clauses: [],
	        comment: null,
	        attribution,
	    }));
	}
	function datumOf(decision, input, output, slice) {
	    if (decision.anchor.kind === consensus_types_1.AnchorKind.Row) {
	        const existence = decision.existence ?? package_data_adjudication_types_1.RowExistence.Present;
	        return {
	            kind: consensus_types_1.AnchorKind.Row,
	            existence,
	            rowContentRaw: existence === package_data_adjudication_types_1.RowExistence.Absent ? null : rowContentOf(input, output, decision.anchor.rowKey),
	        };
	    }
	    const raw = decision.rawValue ?? null;
	    return {
	        kind: consensus_types_1.AnchorKind.Field,
	        value: { raw, normalized: (0, adjudication_slice_1.normalizedCellValueOf)(raw, decision.anchor, input, slice) },
	    };
	}
	function rowContentOf(input, output, rowKey) {
	    const projected = input.rows.find((row) => row.rowKey === rowKey)?.values;
	    if (projected !== undefined) {
	        return projected;
	    }
	    return (0, adjudication_admitted_rows_1.admittedRowContentOf)(input, output, rowKey);
	}
	function errorDatumOf(anchor, rawValue, input, output, slice, settledExistences) {
	    if (anchor.kind === consensus_types_1.AnchorKind.Row) {
	        const settled = settledExistences.get(anchor.rowKey);
	        if (settled === undefined) {
	            throw new Error(`Row '${anchor.rowKey}' was ruled wrong without its ground truth being settled, so there is ` +
	                'no claim for the ruling to contradict.');
	        }
	        const existence = settled === package_data_adjudication_types_1.RowExistence.Present ? package_data_adjudication_types_1.RowExistence.Absent : package_data_adjudication_types_1.RowExistence.Present;
	        return {
	            kind: consensus_types_1.AnchorKind.Row,
	            existence,
	            rowContentRaw: existence === package_data_adjudication_types_1.RowExistence.Absent ? null : rowContentOf(input, output, anchor.rowKey),
	        };
	    }
	    return {
	        kind: consensus_types_1.AnchorKind.Field,
	        value: { raw: rawValue ?? null, normalized: (0, adjudication_slice_1.normalizedCellValueOf)(rawValue, anchor, input, slice) },
	    };
	}
	function settledRowExistences(input, output) {
	    const settled = new Map();
	    for (const standing of input.standings) {
	        const datum = standing.groundTruthDatum;
	        if (standing.anchor.kind !== consensus_types_1.AnchorKind.Row || datum === null || datum.kind !== consensus_types_1.AnchorKind.Row) {
	            continue;
	        }
	        settled.set(standing.anchor.rowKey, datum.existence);
	    }
	    for (const decision of output.groundTruthDecisions) {
	        if (decision.anchor.kind === consensus_types_1.AnchorKind.Row && decision.existence !== undefined) {
	            settled.set(decision.anchor.rowKey, decision.existence);
	        }
	    }
	    return settled;
	}
	function addressOf(input, anchor) {
	    return {
	        packageId: input.packageId,
	        sliceKey: input.sliceKey,
	        sliceVersion: input.sliceVersion,
	        anchor,
	    };
	}
	function isSuppressed(anchor, absentRowKeys) {
	    return anchor.kind === consensus_types_1.AnchorKind.Field && absentRowKeys.has(anchor.rowKey);
	}
	
	return adjudicationEncode;
}

var adjudicationValidate = {};

var hasRequiredAdjudicationValidate;

function requireAdjudicationValidate () {
	if (hasRequiredAdjudicationValidate) return adjudicationValidate;
	hasRequiredAdjudicationValidate = 1;
	Object.defineProperty(adjudicationValidate, "__esModule", { value: true });
	adjudicationValidate.AdjudicationOutputIssueKind = void 0;
	adjudicationValidate.validateAdjudicationOutput = validateAdjudicationOutput;
	const consensus_types_1 = requireConsensus_types();
	const adjudication_admitted_rows_1 = requireAdjudicationAdmittedRows();
	const adjudication_questions_1 = requireAdjudicationQuestions();
	const adjudication_roster_1 = requireAdjudicationRoster();
	const adjudication_slice_1 = requireAdjudicationSlice();
	const adjudication_step_types_1 = requireAdjudicationStep_types();
	var AdjudicationOutputIssueKind;
	(function (AdjudicationOutputIssueKind) {
	    AdjudicationOutputIssueKind["UnanchoredDecision"] = "unanchored_decision";
	    AdjudicationOutputIssueKind["DatumAnchorMismatch"] = "datum_anchor_mismatch";
	    AdjudicationOutputIssueKind["InadmissibleValue"] = "inadmissible_value";
	    AdjudicationOutputIssueKind["DuplicateDecision"] = "duplicate_decision";
	    AdjudicationOutputIssueKind["MalformedSignalDecision"] = "malformed_signal_decision";
	    AdjudicationOutputIssueKind["SignalNameCollision"] = "signal_name_collision";
	    AdjudicationOutputIssueKind["DeletedSignalStillAssigned"] = "deleted_signal_still_assigned";
	    AdjudicationOutputIssueKind["RowWithoutContent"] = "row_without_content";
	})(AdjudicationOutputIssueKind || (adjudicationValidate.AdjudicationOutputIssueKind = AdjudicationOutputIssueKind = {}));
	function validateAdjudicationOutput(input, output) {
	    const questions = (0, adjudication_questions_1.buildAdjudicationQuestions)(input, output);
	    const questionKeys = new Set(questions.map((question) => question.questionKey));
	    const raisedAnchors = new Set(questions.map((question) => (0, consensus_types_1.anchorKey)(question.anchor)));
	    return [
	        ...groundTruthIssues(output.groundTruthDecisions, questionKeys, (0, adjudication_slice_1.adjudicationSliceOf)(input)),
	        ...admittedRowIssues(input, output),
	        ...errorIssues(output.errorDecisions, raisedAnchors),
	        ...signalIssues(input, output),
	    ];
	}
	function signalIssues(input, output) {
	    const byId = new Map(input.roster.map((signal) => [signal.signalId, signal]));
	    const pinned = (0, adjudication_roster_1.pinnedSignalNameKeysAfter)(input, output);
	    const issues = [];
	    const claimedBy = new Map();
	    for (const decision of (0, adjudication_roster_1.effectiveSignalDecisions)(input, output)) {
	        const questionKey = (0, adjudication_questions_1.signalQuestionKeyOf)(decision.name ?? '');
	        const describe = decision.name ?? decision.signalId ?? '(unnamed)';
	        const ruled = (0, adjudication_roster_1.isSignalRuled)(decision);
	        if (!ruled) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.MalformedSignalDecision,
	                questionKey,
	                detail: `The '${decision.kind}' disposition for '${describe}' is missing what that kind needs.`,
	            });
	            continue;
	        }
	        if (decision.signalId !== undefined && !byId.has(decision.signalId)) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.MalformedSignalDecision,
	                questionKey,
	                detail: `Signal '${decision.signalId}' is not one this package holds.`,
	            });
	            continue;
	        }
	        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Delete) {
	            issues.push(...deletionIssues(decision, byId, pinned, questionKey));
	            continue;
	        }
	        const claimant = claimedBy.get(questionKey);
	        if (claimant !== undefined) {
	            issues.push(collisionIssue(claimant, decision, byId, questionKey));
	            continue;
	        }
	        claimedBy.set(questionKey, decision);
	    }
	    return issues;
	}
	function collisionIssue(first, second, byId, questionKey) {
	    const name = second.name ?? first.name ?? '';
	    const renamedIds = new Set([first, second].flatMap((decision) => decision.kind === adjudication_step_types_1.SignalDispositionKind.Rename && decision.signalId !== undefined ? [decision.signalId] : []));
	    if (renamedIds.size < 2) {
	        return {
	            kind: AdjudicationOutputIssueKind.DuplicateDecision,
	            questionKey,
	            detail: `The signal name '${name}' is disposed of more than once.`,
	        };
	    }
	    const named = [...renamedIds].map((id) => byId.get(id)?.name ?? id);
	    return {
	        kind: AdjudicationOutputIssueKind.SignalNameCollision,
	        questionKey,
	        detail: `Signals '${named.join("' and '")}' would both become '${name}'. Merging two signals is not a ` +
	            'ruling this review can make.',
	    };
	}
	function deletionIssues(decision, byId, pinned, questionKey) {
	    const signal = decision.signalId === undefined ? undefined : byId.get(decision.signalId);
	    if (signal === undefined || !pinned.has((0, adjudication_roster_1.signalIdentityKey)(signal.name))) {
	        return [];
	    }
	    return [
	        {
	            kind: AdjudicationOutputIssueKind.DeletedSignalStillAssigned,
	            questionKey,
	            detail: `Signal '${signal.name}' cannot be deleted while this review still assigns it to a pin.`,
	        },
	    ];
	}
	function groundTruthQuestionKey(anchor) {
	    return anchor.kind === consensus_types_1.AnchorKind.Row ?
	        (0, adjudication_questions_1.questionKeyOf)(adjudication_questions_1.AdjudicationQuestionKind.GroundTruthExistence, anchor)
	        : (0, adjudication_questions_1.questionKeyOf)(adjudication_questions_1.AdjudicationQuestionKind.GroundTruthValue, anchor);
	}
	function groundTruthIssues(decisions, questionKeys, slice) {
	    const issues = [];
	    const seen = new Set();
	    for (const decision of decisions) {
	        const questionKey = groundTruthQuestionKey(decision.anchor);
	        const anchor = decision.anchor;
	        if (!questionKeys.has(questionKey)) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.UnanchoredDecision,
	                anchor,
	                questionKey,
	                detail: `The review asks nothing about the ground truth at '${(0, consensus_types_1.anchorKey)(anchor)}'.`,
	            });
	            continue;
	        }
	        const mismatch = groundTruthDatumMismatch(decision);
	        if (mismatch !== null) {
	            issues.push({ kind: AdjudicationOutputIssueKind.DatumAnchorMismatch, anchor, questionKey, detail: mismatch });
	        }
	        if (anchor.kind === consensus_types_1.AnchorKind.Field &&
	            decision.rawValue !== undefined &&
	            !slice.admitsValue(anchor.column, decision.rawValue)) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.InadmissibleValue,
	                anchor,
	                questionKey,
	                detail: `${JSON.stringify(decision.rawValue)} is not a value '${anchor.column}' can hold.`,
	            });
	        }
	        if (seen.has(questionKey)) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.DuplicateDecision,
	                anchor,
	                questionKey,
	                detail: `Ground truth at '${(0, consensus_types_1.anchorKey)(anchor)}' is ruled more than once.`,
	            });
	        }
	        seen.add(questionKey);
	    }
	    return issues;
	}
	function admittedRowIssues(input, output) {
	    return [...(0, adjudication_admitted_rows_1.admittedRowKeysOf)(input, output)].flatMap((rowKey) => {
	        if ((0, adjudication_admitted_rows_1.admittedRowContentOf)(input, output, rowKey) !== null) {
	            return [];
	        }
	        const anchor = { kind: consensus_types_1.AnchorKind.Row, rowKey };
	        return [
	            {
	                kind: AdjudicationOutputIssueKind.RowWithoutContent,
	                anchor,
	                questionKey: groundTruthQuestionKey(anchor),
	                detail: `Row '${rowKey}' is ruled present, but the package does not hold it and nothing records ` +
	                    'what it contains, so it cannot be added. Rule it absent instead.',
	            },
	        ];
	    });
	}
	function groundTruthDatumMismatch(decision) {
	    if (decision.anchor.kind === consensus_types_1.AnchorKind.Row) {
	        if (decision.existence === undefined) {
	            return "A row ruling must state the row's existence.";
	        }
	        return decision.rawValue === undefined ? null : 'A row ruling cannot carry a cell value.';
	    }
	    if (decision.rawValue === undefined) {
	        return "A cell ruling must state the cell's value.";
	    }
	    return decision.existence === undefined ? null : 'A cell ruling cannot carry a row existence.';
	}
	function errorIssues(decisions, raisedAnchors) {
	    const issues = [];
	    const seen = new Set();
	    for (const decision of decisions) {
	        const anchor = decision.anchor;
	        const questionKey = (0, adjudication_questions_1.questionKeyOf)(adjudication_questions_1.AdjudicationQuestionKind.InferenceError, anchor, decision.rawValue);
	        if (!raisedAnchors.has((0, consensus_types_1.anchorKey)(anchor))) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.UnanchoredDecision,
	                anchor,
	                questionKey,
	                detail: `The review asks nothing about '${(0, consensus_types_1.anchorKey)(anchor)}'.`,
	            });
	            continue;
	        }
	        if (anchor.kind === consensus_types_1.AnchorKind.Row && decision.rawValue !== null) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.DatumAnchorMismatch,
	                anchor,
	                questionKey,
	                detail: 'A row ruling cannot carry a cell value.',
	            });
	        }
	        if (seen.has(questionKey)) {
	            issues.push({
	                kind: AdjudicationOutputIssueKind.DuplicateDecision,
	                anchor,
	                questionKey,
	                detail: `The value at '${(0, consensus_types_1.anchorKey)(anchor)}' is ruled wrong more than once.`,
	            });
	        }
	        seen.add(questionKey);
	    }
	    return issues;
	}
	
	return adjudicationValidate;
}

var adjudicationSteps = {};

var hasRequiredAdjudicationSteps;

function requireAdjudicationSteps () {
	if (hasRequiredAdjudicationSteps) return adjudicationSteps;
	hasRequiredAdjudicationSteps = 1;
	Object.defineProperty(adjudicationSteps, "__esModule", { value: true });
	adjudicationSteps.AdjudicationStepDefinitions = void 0;
	const adjudication_step_types_1 = requireAdjudicationStep_types();
	adjudicationSteps.AdjudicationStepDefinitions = {
	    PackageDataAdjudication: {
	        type: 'PackageDataAdjudication',
	        displayName: 'Package data adjudication',
	        inputSchema: adjudication_step_types_1.AdjudicationReviewInputSchema,
	        outputSchema: adjudication_step_types_1.AdjudicationReviewOutputSchema,
	    },
	};
	
	return adjudicationSteps;
}

var hasRequiredAdjudication;

function requireAdjudication () {
	if (hasRequiredAdjudication) return adjudication;
	hasRequiredAdjudication = 1;
	(function (exports) {
		var __createBinding = (adjudication && adjudication.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (adjudication && adjudication.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireAdjudicationView_types(), exports);
		__exportStar(requireAdjudicationDiscard_types(), exports);
		__exportStar(requireAdjudicationStep_types(), exports);
		__exportStar(requireAdjudicationRoster(), exports);
		__exportStar(requireAdjudicationSlice(), exports);
		__exportStar(requireAdjudicationAdmittedRows(), exports);
		__exportStar(requireAdjudicationQuestions(), exports);
		__exportStar(requireAdjudicationEncode(), exports);
		__exportStar(requireAdjudicationValidate(), exports);
		__exportStar(requireAdjudicationSteps(), exports);
		
	} (adjudication));
	return adjudication;
}

var review = {};

var implementationRulesReview_types = {};

var hasRequiredImplementationRulesReview_types;

function requireImplementationRulesReview_types () {
	if (hasRequiredImplementationRulesReview_types) return implementationRulesReview_types;
	hasRequiredImplementationRulesReview_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ImplementationRulesReviewInputSchema = exports.ImplementationRulesReviewOutputSchema = exports.ImplementationRuleSectionReviewDraftSchema = exports.ImplementationRuleReviewDraftSchema = exports.GroupReviewStateSchema = exports.ExcerptReviewStateSchema = exports.RuleCardStateSchema = exports.ExcerptDecisionSchema = exports.ExcerptDecision = exports.CardVerdictSchema = exports.CardVerdict = exports.DatasheetExcerptCollectionSchema = exports.ExcerptPageSchema = exports.ExcerptSchema = exports.FormulaConfirmationSchema = exports.FormulaConfirmation = exports.ExcerptKindSchema = exports.ExcerptKind = exports.IMPLEMENTATION_RULES_DATASHEET_EXCERPT_VERSION = void 0;
		const sigma_engine_shared_1 = requireDist$1();
		const ImplementationRules = requireImplementationRules();
		const z = /*@__PURE__*/ requireZod();
		exports.IMPLEMENTATION_RULES_DATASHEET_EXCERPT_VERSION = 'v4';
		var ExcerptKind;
		(function (ExcerptKind) {
		    ExcerptKind["Text"] = "text";
		    ExcerptKind["TableRow"] = "table_row";
		    ExcerptKind["Figure"] = "figure";
		    ExcerptKind["Formula"] = "formula";
		    ExcerptKind["Table"] = "table";
		})(ExcerptKind || (exports.ExcerptKind = ExcerptKind = {}));
		exports.ExcerptKindSchema = z.nativeEnum(ExcerptKind);
		var FormulaConfirmation;
		(function (FormulaConfirmation) {
		    FormulaConfirmation["Confirmed"] = "confirmed";
		    FormulaConfirmation["Uncertain"] = "uncertain";
		    FormulaConfirmation["Unclassified"] = "unclassified";
		})(FormulaConfirmation || (exports.FormulaConfirmation = FormulaConfirmation = {}));
		exports.FormulaConfirmationSchema = z.nativeEnum(FormulaConfirmation);
		exports.ExcerptSchema = z.object({
		    id: z.string(),
		    pageNumber: z.number().int().positive(),
		    kind: exports.ExcerptKindSchema,
		    elementIndex: z.number().int().nonnegative(),
		    rowIndex: z.number().int().nonnegative().nullable(),
		    text: z.string(),
		    continuation: z.string().nullable(),
		    continuationElementIndex: z.number().int().nonnegative().nullable(),
		    matchedSentences: z.string().array(),
		    taxon: z.string(),
		    figureImage: z.string().nullable(),
		    formulaConfirmation: exports.FormulaConfirmationSchema.nullable(),
		});
		exports.ExcerptPageSchema = z.object({
		    pageNumber: z.number().int().positive(),
		    title: z.string().nullable(),
		});
		exports.DatasheetExcerptCollectionSchema = z.object({
		    fileId: z.string(),
		    excerptVersion: z.string(),
		    pages: exports.ExcerptPageSchema.array(),
		    excerpts: exports.ExcerptSchema.array(),
		});
		var CardVerdict;
		(function (CardVerdict) {
		    CardVerdict["Confirmed"] = "confirmed";
		    CardVerdict["Edited"] = "edited";
		    CardVerdict["Rejected"] = "rejected";
		})(CardVerdict || (exports.CardVerdict = CardVerdict = {}));
		exports.CardVerdictSchema = z.nativeEnum(CardVerdict);
		var ExcerptDecision;
		(function (ExcerptDecision) {
		    ExcerptDecision["Dismissed"] = "dismissed";
		    ExcerptDecision["RuleCreated"] = "rule_created";
		})(ExcerptDecision || (exports.ExcerptDecision = ExcerptDecision = {}));
		exports.ExcerptDecisionSchema = z.nativeEnum(ExcerptDecision);
		exports.RuleCardStateSchema = z.object({
		    verdict: exports.CardVerdictSchema.optional(),
		    note: z.string().optional(),
		});
		exports.ExcerptReviewStateSchema = z.object({
		    decision: exports.ExcerptDecisionSchema.optional(),
		    ruleIds: z.string().array().optional(),
		});
		exports.GroupReviewStateSchema = z.object({
		    reviewed: z.boolean(),
		    auto: z.boolean(),
		});
		exports.ImplementationRuleReviewDraftSchema = z.object({
		    id: z.string(),
		    text: z.string(),
		    modality: ImplementationRules.RuleModalitySchema.optional(),
		    citations: ImplementationRules.RuleCitationSchema.array().optional(),
		});
		exports.ImplementationRuleSectionReviewDraftSchema = z.object({
		    sectionId: z.string(),
		    sectionSlug: z.string(),
		    rules: exports.ImplementationRuleReviewDraftSchema.array(),
		});
		exports.ImplementationRulesReviewOutputSchema = z.object({
		    excerptVersion: z.string().optional(),
		    rules: exports.ImplementationRuleSectionReviewDraftSchema.array().optional(),
		    cards: z.record(z.string(), exports.RuleCardStateSchema).optional(),
		    excerpts: z.record(z.string(), exports.ExcerptReviewStateSchema).optional(),
		    groups: z.record(z.string(), exports.GroupReviewStateSchema).optional(),
		    pickedExcerpts: z.record(z.string(), exports.ExcerptSchema).optional(),
		    replaceChangedRules: z.boolean().optional(),
		});
		exports.ImplementationRulesReviewInputSchema = z.object({
		    packageId: z.string().min(1),
		    datasheetFileId: z.string().min(1),
		    manufacturerPartNumber: z.string().min(1),
		    signals: sigma_engine_shared_1.DesignModels.SignalSchema.array(),
		    implementationRules: ImplementationRules.ImplementationRuleSectionSchema.array(),
		    implementationRulesContentHash: z.string().min(1),
		});
		
	} (implementationRulesReview_types));
	return implementationRulesReview_types;
}

var implementationRulesReviewValidate = {};

var hasRequiredImplementationRulesReviewValidate;

function requireImplementationRulesReviewValidate () {
	if (hasRequiredImplementationRulesReviewValidate) return implementationRulesReviewValidate;
	hasRequiredImplementationRulesReviewValidate = 1;
	Object.defineProperty(implementationRulesReviewValidate, "__esModule", { value: true });
	implementationRulesReviewValidate.ImplementationRulesReviewIssueCode = void 0;
	implementationRulesReviewValidate.validateImplementationRulesReviewOutput = validateImplementationRulesReviewOutput;
	implementationRulesReviewValidate.finalRuleSections = finalRuleSections;
	const ImplementationRules = requireImplementationRules();
	const implementation_rules_review_types_1 = requireImplementationRulesReview_types();
	var ImplementationRulesReviewIssueCode;
	(function (ImplementationRulesReviewIssueCode) {
	    ImplementationRulesReviewIssueCode["MissingExcerptVersion"] = "missing_excerpt_version";
	    ImplementationRulesReviewIssueCode["NoGroups"] = "no_groups";
	    ImplementationRulesReviewIssueCode["GroupNotReviewed"] = "group_not_reviewed";
	    ImplementationRulesReviewIssueCode["MissingVerdict"] = "missing_verdict";
	    ImplementationRulesReviewIssueCode["MissingRule"] = "missing_rule";
	    ImplementationRulesReviewIssueCode["MissingRejectNote"] = "missing_reject_note";
	    ImplementationRulesReviewIssueCode["InvalidRule"] = "invalid_rule";
	    ImplementationRulesReviewIssueCode["DuplicateRuleId"] = "duplicate_rule_id";
	    ImplementationRulesReviewIssueCode["DuplicateSectionId"] = "duplicate_section_id";
	    ImplementationRulesReviewIssueCode["InvalidSectionSlug"] = "invalid_section_slug";
	    ImplementationRulesReviewIssueCode["DuplicateSectionSlug"] = "duplicate_section_slug";
	})(ImplementationRulesReviewIssueCode || (implementationRulesReviewValidate.ImplementationRulesReviewIssueCode = ImplementationRulesReviewIssueCode = {}));
	const SECTION_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
	function validateImplementationRulesReviewOutput(input, output) {
	    return [...progressIssues(output), ...verdictIssues(input, output), ...ruleSetIssues(output, input)];
	}
	function finalRuleSections(input, output) {
	    const issues = validateImplementationRulesReviewOutput(input, output);
	    if (issues.length > 0) {
	        throw new Error(`Implementation-rules review cannot be finalized: ${issues.map((issue) => issue.detail).join('; ')}`);
	    }
	    return ImplementationRules.ImplementationRuleSectionsSchema.parse(nonEmptyKeptDrafts(input, output));
	}
	function progressIssues(output) {
	    const issues = [];
	    if (output.excerptVersion === undefined || output.excerptVersion.trim() === '') {
	        issues.push({
	            code: ImplementationRulesReviewIssueCode.MissingExcerptVersion,
	            detail: 'The review does not record the excerpt version it was made against.',
	        });
	    }
	    const groups = Object.entries(output.groups ?? {});
	    if (groups.length === 0) {
	        issues.push({
	            code: ImplementationRulesReviewIssueCode.NoGroups,
	            detail: 'The review records no excerpt groups.',
	        });
	    }
	    for (const [groupId, group] of groups) {
	        if (!group.reviewed) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.GroupNotReviewed,
	                detail: `Excerpt group '${groupId}' is not marked reviewed.`,
	            });
	        }
	    }
	    return issues;
	}
	function verdictIssues(input, output) {
	    const issues = [];
	    const editedIds = new Set(editedSections(input, output).flatMap((section) => section.rules.map((rule) => rule.id)));
	    for (const rule of input.implementationRules.flatMap((section) => section.rules)) {
	        const verdict = output.cards?.[rule.id]?.verdict;
	        if (verdict === undefined) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.MissingVerdict,
	                detail: `Rule '${rule.id}' has no verdict.`,
	            });
	        }
	        else if (verdict !== implementation_rules_review_types_1.CardVerdict.Rejected && !editedIds.has(rule.id)) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.MissingRule,
	                detail: `Rule '${rule.id}' is missing from the edited rules but was not rejected.`,
	            });
	        }
	    }
	    for (const ruleId of editedIds) {
	        const card = output.cards?.[ruleId];
	        if (card?.verdict === implementation_rules_review_types_1.CardVerdict.Rejected && (card.note ?? '').trim() === '') {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.MissingRejectNote,
	                detail: `Rejected rule '${ruleId}' has no note saying why.`,
	            });
	        }
	    }
	    return issues;
	}
	function ruleSetIssues(output, input) {
	    const issues = [];
	    const seenRuleIds = new Set();
	    for (const rule of editedSections(input, output).flatMap((section) => section.rules)) {
	        if (seenRuleIds.has(rule.id)) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.DuplicateRuleId,
	                detail: `More than one rule has id '${rule.id}'.`,
	            });
	        }
	        seenRuleIds.add(rule.id);
	    }
	    const seenSectionIds = new Set();
	    const seenSlugs = new Set();
	    for (const section of nonEmptyKeptDrafts(input, output)) {
	        for (const rule of section.rules) {
	            const parsed = ImplementationRules.ImplementationRuleSchema.safeParse(rule);
	            if (!parsed.success) {
	                issues.push({
	                    code: ImplementationRulesReviewIssueCode.InvalidRule,
	                    detail: `Rule '${rule.id}' is not a valid rule: ${parsed.error.issues
	                        .map((zodIssue) => zodIssue.message)
	                        .join('; ')}`,
	                });
	            }
	        }
	        if (seenSectionIds.has(section.sectionId)) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.DuplicateSectionId,
	                detail: `More than one section has id '${section.sectionId}'.`,
	            });
	        }
	        seenSectionIds.add(section.sectionId);
	        if (!SECTION_SLUG_PATTERN.test(section.sectionSlug)) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.InvalidSectionSlug,
	                detail: `Section slug '${section.sectionSlug}' is not lower-case kebab case.`,
	            });
	        }
	        else if (seenSlugs.has(section.sectionSlug)) {
	            issues.push({
	                code: ImplementationRulesReviewIssueCode.DuplicateSectionSlug,
	                detail: `More than one section has slug '${section.sectionSlug}'.`,
	            });
	        }
	        seenSlugs.add(section.sectionSlug);
	    }
	    return issues;
	}
	function editedSections(input, output) {
	    return output.rules ?? input.implementationRules;
	}
	function nonEmptyKeptDrafts(input, output) {
	    const isKept = (rule) => output.cards?.[rule.id]?.verdict !== implementation_rules_review_types_1.CardVerdict.Rejected;
	    return editedSections(input, output)
	        .map((section) => ({ ...section, rules: section.rules.filter(isKept) }))
	        .filter((section) => section.rules.length > 0);
	}
	
	return implementationRulesReviewValidate;
}

var implementationRulesReviewResult_types = {};

var hasRequiredImplementationRulesReviewResult_types;

function requireImplementationRulesReviewResult_types () {
	if (hasRequiredImplementationRulesReviewResult_types) return implementationRulesReviewResult_types;
	hasRequiredImplementationRulesReviewResult_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.ImplementationRulesReviewResultSchema = exports.FilterMissSchema = exports.FilterMissRuleSchema = exports.ImplementationRulesReviewSummarySchema = exports.FormulaExcerptCountsSchema = exports.AiRuleCountsSchema = exports.KeptRuleCountsSchema = exports.SurfacedContentCountsSchema = exports.RuleAuthorSchema = exports.RuleAuthor = exports.ExcerptSkipReasonSchema = exports.ExcerptSkipReason = void 0;
		const z = /*@__PURE__*/ requireZod();
		var ExcerptSkipReason;
		(function (ExcerptSkipReason) {
		    ExcerptSkipReason["ExcludedSection"] = "excluded_section";
		    ExcerptSkipReason["NoNormativeWording"] = "no_normative_wording";
		    ExcerptSkipReason["InsideTable"] = "inside_table";
		    ExcerptSkipReason["InsideFigure"] = "inside_figure";
		    ExcerptSkipReason["FigureCaptionNotMatched"] = "figure_caption_not_matched";
		    ExcerptSkipReason["FormulaRejected"] = "formula_rejected";
		    ExcerptSkipReason["AlreadyPackageData"] = "already_package_data";
		    ExcerptSkipReason["EmptyText"] = "empty_text";
		    ExcerptSkipReason["UnsupportedElementType"] = "unsupported_element_type";
		    ExcerptSkipReason["NoElementCited"] = "no_element_cited";
		    ExcerptSkipReason["ElementNotFound"] = "element_not_found";
		    ExcerptSkipReason["OtherDocument"] = "other_document";
		})(ExcerptSkipReason || (exports.ExcerptSkipReason = ExcerptSkipReason = {}));
		exports.ExcerptSkipReasonSchema = z.nativeEnum(ExcerptSkipReason);
		var RuleAuthor;
		(function (RuleAuthor) {
		    RuleAuthor["Ai"] = "ai";
		    RuleAuthor["Reviewer"] = "reviewer";
		})(RuleAuthor || (exports.RuleAuthor = RuleAuthor = {}));
		exports.RuleAuthorSchema = z.nativeEnum(RuleAuthor);
		exports.SurfacedContentCountsSchema = z.object({
		    surfaced: z.number().int().nonnegative(),
		    citedByKeptRule: z.number().int().nonnegative(),
		    dismissed: z.number().int().nonnegative(),
		});
		exports.KeptRuleCountsSchema = z.object({
		    kept: z.number().int().nonnegative(),
		    filterReached: z.number().int().nonnegative(),
		    filterMissed: z.number().int().nonnegative(),
		});
		exports.AiRuleCountsSchema = z.object({
		    proposed: z.number().int().nonnegative(),
		    confirmed: z.number().int().nonnegative(),
		    edited: z.number().int().nonnegative(),
		    rejected: z.number().int().nonnegative(),
		    reviewerAdded: z.number().int().nonnegative(),
		});
		exports.FormulaExcerptCountsSchema = z.object({
		    confirmed: z.number().int().nonnegative(),
		    confirmedCitedByKeptRule: z.number().int().nonnegative(),
		    uncertain: z.number().int().nonnegative(),
		    uncertainCitedByKeptRule: z.number().int().nonnegative(),
		    unclassified: z.number().int().nonnegative(),
		    unclassifiedCitedByKeptRule: z.number().int().nonnegative(),
		});
		exports.ImplementationRulesReviewSummarySchema = z.object({
		    content: exports.SurfacedContentCountsSchema,
		    rules: exports.KeptRuleCountsSchema,
		    ai: exports.AiRuleCountsSchema,
		    formulas: exports.FormulaExcerptCountsSchema,
		});
		exports.FilterMissRuleSchema = z.object({
		    id: z.string(),
		    text: z.string(),
		    authoredBy: exports.RuleAuthorSchema,
		});
		exports.FilterMissSchema = z.object({
		    pageNumber: z.number().int().positive().nullable(),
		    elementIndex: z.number().int().nonnegative().nullable(),
		    rowIndex: z.number().int().nonnegative().nullable(),
		    whyFilterSkipped: exports.ExcerptSkipReasonSchema,
		    keptRules: exports.FilterMissRuleSchema.array(),
		});
		exports.ImplementationRulesReviewResultSchema = z.object({
		    planId: z.string(),
		    fragmentName: z.string(),
		    packageId: z.string(),
		    datasheetFileId: z.string(),
		    excerptVersion: z.string(),
		    completedByUserId: z.string().nullable(),
		    summary: exports.ImplementationRulesReviewSummarySchema,
		    filterMisses: exports.FilterMissSchema.array(),
		});
		
	} (implementationRulesReviewResult_types));
	return implementationRulesReviewResult_types;
}

var hasRequiredReview;

function requireReview () {
	if (hasRequiredReview) return review;
	hasRequiredReview = 1;
	(function (exports) {
		var __createBinding = (review && review.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (review && review.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireImplementationRulesReview_types(), exports);
		__exportStar(requireImplementationRulesReviewValidate(), exports);
		__exportStar(requireImplementationRulesReviewResult_types(), exports);
		
	} (review));
	return review;
}

var types = {};

var testExtractSignals = {};

var hasRequiredTestExtractSignals;

function requireTestExtractSignals () {
	if (hasRequiredTestExtractSignals) return testExtractSignals;
	hasRequiredTestExtractSignals = 1;
	Object.defineProperty(testExtractSignals, "__esModule", { value: true });
	testExtractSignals.TestExtractSignalsDefinition = void 0;
	const z = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	const package_common_1 = requirePackageCommon();
	testExtractSignals.TestExtractSignalsDefinition = {
	    type: 'TestExtractSignals',
	    displayName: 'Test Extract Signals',
	    inputSchema: z.object({
	        packageName: z.string(),
	        packageFamilyName: z.string(),
	        datasheetUri: package_common_1.DatasheetUriSchema,
	        sigmaModelType: package_common_1.SigmaModelTypeSchema,
	    }),
	    outputSchema: z.object({
	        signals: z.array(sigma_engine_shared_1.SignalSchema),
	    }),
	};
	
	return testExtractSignals;
}

var testExtractPinout = {};

var hasRequiredTestExtractPinout;

function requireTestExtractPinout () {
	if (hasRequiredTestExtractPinout) return testExtractPinout;
	hasRequiredTestExtractPinout = 1;
	Object.defineProperty(testExtractPinout, "__esModule", { value: true });
	testExtractPinout.TestExtractPinoutDefinition = void 0;
	const z = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	const package_common_1 = requirePackageCommon();
	testExtractPinout.TestExtractPinoutDefinition = {
	    type: 'TestExtractPinout',
	    displayName: 'Test Extract Pinout',
	    inputSchema: z.object({
	        packageName: z.string(),
	        packageFamilyName: z.string(),
	        datasheetUri: package_common_1.DatasheetUriSchema,
	        signals: z.array(sigma_engine_shared_1.SignalSchema),
	    }),
	    outputSchema: z.object({
	        pinout: package_common_1.PinoutSchema,
	    }),
	};
	
	return testExtractPinout;
}

var extractSteps = {};

var hasRequiredExtractSteps;

function requireExtractSteps () {
	if (hasRequiredExtractSteps) return extractSteps;
	hasRequiredExtractSteps = 1;
	Object.defineProperty(extractSteps, "__esModule", { value: true });
	extractSteps.ExtractStepDefinitions = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const parent_rating_row_1 = requireParentRatingRow();
	const extract_floats_and_ties_types_1 = requireExtractFloatsAndTies_types();
	const misc_1 = requireMisc();
	extractSteps.ExtractStepDefinitions = {
	    ExtractAmrs: {
	        type: 'ExtractAmrs',
	        displayName: 'Extract absolute maximum ratings',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	            modes: sigma_engine_shared_1.ModeBaseSchema.array(),
	            sequenceGate: misc_1.sequenceGateSchema,
	            parentAmrRows: parent_rating_row_1.ParentRatingRowSchema.array().optional(),
	        }),
	        outputSchema: z.object({
	            boundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
	        }),
	    },
	    ExtractOcs: {
	        type: 'ExtractOcs',
	        displayName: 'Extract operating conditions',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	            modes: sigma_engine_shared_1.ModeBaseSchema.array(),
	            sequenceGate: misc_1.sequenceGateSchema,
	            parentOcRows: parent_rating_row_1.ParentRatingRowSchema.array().optional(),
	        }),
	        outputSchema: z.object({
	            boundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
	        }),
	    },
	    ExtractFloatsAndTies: {
	        type: 'ExtractFloatsAndTies',
	        displayName: 'Extract floats and ties',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	            pinout: misc_1.pkgPinoutSchema,
	        }),
	        outputSchema: z.object({
	            floatsAndTies: extract_floats_and_ties_types_1.FloatsAndTiesSchema,
	        }),
	    },
	    ExtractFunctions: {
	        type: 'ExtractFunctions',
	        displayName: 'Extract functions',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            sigmaModelType: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	            resources: sigma_engine_shared_1.IComponentResourceSchema.array(),
	        }),
	        outputSchema: z.object({
	            functions: misc_1.pkgFunctionsSchema,
	        }),
	    },
	    ExtractParameters: {
	        type: 'ExtractParameters',
	        displayName: 'Extract parameters',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            sigmaModelType: z.string(),
	            sequenceGate: misc_1.sequenceGateSchema,
	        }),
	        outputSchema: z.object({
	            parameters: misc_1.pkgParametersSchema,
	        }),
	    },
	    ExtractPinout: {
	        type: 'ExtractPinout',
	        displayName: 'Extract pinout',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	        }),
	        outputSchema: z.object({
	            pinout: misc_1.pkgPinoutSchema,
	        }),
	    },
	    ExtractResources: {
	        type: 'ExtractResources',
	        displayName: 'Extract resources',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            sequenceGate: misc_1.sequenceGateSchema,
	        }),
	        outputSchema: z.object({
	            resources: sigma_engine_shared_1.IComponentResourceSchema.array(),
	        }),
	    },
	    ExtractSignalDriveModes: {
	        type: 'ExtractSignalDriveModes',
	        displayName: 'Extract signal drive modes (legacy)',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	            modes: sigma_engine_shared_1.ModeBaseSchema.array(),
	            sequenceGate: misc_1.sequenceGateSchema,
	        }),
	        outputSchema: z.object({
	            signals: sigma_engine_shared_1.SignalSchema.array().nullable(),
	        }),
	    },
	    ExtractSignals: {
	        type: 'ExtractSignals',
	        displayName: 'Extract signals',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            sigmaModelType: z.string(),
	        }),
	        outputSchema: z.object({
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	        }),
	    },
	};
	
	return extractSteps;
}

var formSteps = {};

var hasRequiredFormSteps;

function requireFormSteps () {
	if (hasRequiredFormSteps) return formSteps;
	hasRequiredFormSteps = 1;
	Object.defineProperty(formSteps, "__esModule", { value: true });
	formSteps.FormStepDefinitions = void 0;
	const step_definition_1 = requireStepDefinition$2();
	const step_definition_2 = requireStepDefinition$1();
	const step_definition_3 = requireStepDefinition();
	formSteps.FormStepDefinitions = {
	    NormalizedAmrsForm: step_definition_1.NormalizedAmrsFormDefinition,
	    NormalizedOcsForm: step_definition_1.NormalizedOcsFormDefinition,
	    NormalizedSignalDriveModesForm: step_definition_2.NormalizedSignalDriveModesFormDefinition,
	    SigmaModelEntryForm: step_definition_3.SigmaModelEntryFormDefinition,
	};
	
	return formSteps;
}

var miscSteps = {};

var hasRequiredMiscSteps;

function requireMiscSteps () {
	if (hasRequiredMiscSteps) return miscSteps;
	hasRequiredMiscSteps = 1;
	Object.defineProperty(miscSteps, "__esModule", { value: true });
	miscSteps.MiscStepDefinitions = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const misc_1 = requireMisc();
	miscSteps.MiscStepDefinitions = {
	    AssignSigmaModelType: {
	        type: 'AssignSigmaModelType',
	        displayName: 'Assign Sigma Model type',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	        }),
	        outputSchema: z.object({
	            sigmaModelType: z.string().min(1, 'sigmaModelType must not be empty'),
	        }),
	    },
	    RateExperience: {
	        type: 'RateExperience',
	        displayName: 'Rate experience',
	        inputSchema: z.object({
	            manufacturerPartNumber: z.string(),
	            amrBoundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
	            ocBoundaries: sigma_engine_shared_1.ComponentBoundarySchema.array(),
	            functions: misc_1.pkgFunctionsSchema,
	            parameters: misc_1.pkgParametersSchema,
	            pinout: misc_1.pkgPinoutSchema,
	            resources: sigma_engine_shared_1.IComponentResourceSchema.array(),
	            sigmaModelType: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	        }),
	        outputSchema: z.object({
	            difficultyRating: z.number().min(1).max(5).nullable(),
	            feedback: z.string().nullable(),
	        }),
	    },
	    ReconcilePackages: {
	        type: 'ReconcilePackages',
	        displayName: 'Reconcile packages',
	        inputSchema: z.object({
	            manufacturerPartNumber: z.string(),
	            pkgABoundaries: misc_1.reconcilePackagesPackageSchema.shape.boundaries,
	            pkgAFunctions: misc_1.reconcilePackagesPackageSchema.shape.functions,
	            pkgAModes: misc_1.reconcilePackagesPackageSchema.shape.modes,
	            pkgAParameters: misc_1.reconcilePackagesPackageSchema.shape.parameters,
	            pkgAPinout: misc_1.reconcilePackagesPackageSchema.shape.pinout,
	            pkgAResources: misc_1.reconcilePackagesPackageSchema.shape.resources,
	            pkgASigmaModelType: misc_1.reconcilePackagesPackageSchema.shape.sigmaModelType,
	            pkgASignals: misc_1.reconcilePackagesPackageSchema.shape.signals,
	            pkgBBoundaries: misc_1.reconcilePackagesPackageSchema.shape.boundaries,
	            pkgBFunctions: misc_1.reconcilePackagesPackageSchema.shape.functions,
	            pkgBModes: misc_1.reconcilePackagesPackageSchema.shape.modes,
	            pkgBParameters: misc_1.reconcilePackagesPackageSchema.shape.parameters,
	            pkgBPinout: misc_1.reconcilePackagesPackageSchema.shape.pinout,
	            pkgBResources: misc_1.reconcilePackagesPackageSchema.shape.resources,
	            pkgBSigmaModelType: misc_1.reconcilePackagesPackageSchema.shape.sigmaModelType,
	            pkgBSignals: misc_1.reconcilePackagesPackageSchema.shape.signals,
	        }),
	        outputSchema: misc_1.reconcilePackagesPackageSchema,
	    },
	};
	
	return miscSteps;
}

var mockSteps = {};

var hasRequiredMockSteps;

function requireMockSteps () {
	if (hasRequiredMockSteps) return mockSteps;
	hasRequiredMockSteps = 1;
	Object.defineProperty(mockSteps, "__esModule", { value: true });
	mockSteps.MockStepDefinitions = void 0;
	const z = /*@__PURE__*/ requireZod();
	mockSteps.MockStepDefinitions = {
	    MockOneInOneOutString: {
	        type: 'MockOneInOneOutString',
	        displayName: 'One in, one out',
	        inputSchema: z.object({
	            in1: z.string(),
	        }),
	        outputSchema: z.object({
	            out1: z.string(),
	        }),
	    },
	    MockOneInTwoOutString: {
	        type: 'MockOneInTwoOutString',
	        displayName: 'One in, two out',
	        inputSchema: z.object({
	            in1: z.string(),
	        }),
	        outputSchema: z.object({
	            out1: z.string(),
	            out2: z.string(),
	        }),
	    },
	    MockTwoInOneOutString: {
	        type: 'MockTwoInOneOutString',
	        displayName: 'Two in, one out',
	        inputSchema: z.object({
	            in1: z.string(),
	            in2: z.string(),
	        }),
	        outputSchema: z.object({
	            out1: z.string(),
	        }),
	    },
	    MockTwoInTwoOutString: {
	        type: 'MockTwoInTwoOutString',
	        displayName: 'Two in, two out',
	        inputSchema: z.object({
	            in1: z.string(),
	            in2: z.string(),
	        }),
	        outputSchema: z.object({
	            out1: z.string(),
	            out2: z.string(),
	        }),
	    },
	    MockTwoInOneOutNumber: {
	        type: 'MockTwoInOneOutNumber',
	        displayName: 'Two in, one out (number)',
	        inputSchema: z.object({
	            in1: z.number(),
	            in2: z.number(),
	        }),
	        outputSchema: z.object({
	            out1: z.number(),
	        }),
	    },
	    MockComplexTypeInOut: {
	        type: 'MockComplexTypeInOut',
	        displayName: 'Complex type in, complex type out',
	        inputSchema: z.object({
	            obj: z.object({
	                str: z.string(),
	                num: z.number(),
	            }),
	            enum: z.enum(['a', 'b', 'c']),
	            array: z.string().array(),
	        }),
	        outputSchema: z.object({
	            obj: z.object({
	                str: z.string(),
	                array: z.string().array(),
	            }),
	        }),
	    },
	};
	
	return mockSteps;
}

var reviewSteps = {};

var hasRequiredReviewSteps;

function requireReviewSteps () {
	if (hasRequiredReviewSteps) return reviewSteps;
	hasRequiredReviewSteps = 1;
	Object.defineProperty(reviewSteps, "__esModule", { value: true });
	reviewSteps.ReviewStepDefinitions = void 0;
	const sigma_engine_shared_1 = requireDist$1();
	const z = /*@__PURE__*/ requireZod();
	const misc_1 = requireMisc();
	const implementation_rules_review_types_1 = requireImplementationRulesReview_types();
	reviewSteps.ReviewStepDefinitions = {
	    ReviewParameters: {
	        type: 'ReviewParameters',
	        displayName: 'Review parameters',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            sigmaModelType: z.string(),
	            parameters: misc_1.pkgParametersSchema,
	            sequenceGate: misc_1.sequenceGateSchema,
	        }),
	        outputSchema: z.object({
	            parameters: misc_1.pkgParametersSchema,
	        }),
	    },
	    ReviewPinout: {
	        type: 'ReviewPinout',
	        displayName: 'Review pinout',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	            pinout: misc_1.pkgPinoutSchema,
	        }),
	        outputSchema: z.object({
	            pinout: misc_1.pkgPinoutSchema,
	        }),
	    },
	    ReviewSignals: {
	        type: 'ReviewSignals',
	        displayName: 'Review signals',
	        inputSchema: z.object({
	            datasheetFileId: z.string(),
	            manufacturerPartNumber: z.string(),
	            sigmaModelType: z.string(),
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	        }),
	        outputSchema: z.object({
	            signals: sigma_engine_shared_1.SignalSchema.array(),
	        }),
	    },
	    ReviewImplementationRules: {
	        type: 'ReviewImplementationRules',
	        displayName: 'Review implementation rules',
	        inputSchema: implementation_rules_review_types_1.ImplementationRulesReviewInputSchema,
	        outputSchema: implementation_rules_review_types_1.ImplementationRulesReviewOutputSchema,
	    },
	};
	
	return reviewSteps;
}

var hasRequiredTypes;

function requireTypes () {
	if (hasRequiredTypes) return types;
	hasRequiredTypes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.stepSchema = exports.StepDefinitions = exports.TestAndMockStepDefinitions = exports.TestStepDefinitions = exports.AbortReason = exports.WizardUsers = void 0;
		const z = /*@__PURE__*/ requireZod();
		const test_extract_signals_1 = requireTestExtractSignals();
		const test_extract_pinout_1 = requireTestExtractPinout();
		const adjudication_steps_1 = requireAdjudicationSteps();
		const exception_review_steps_1 = requireExceptionReviewSteps();
		const extract_steps_1 = requireExtractSteps();
		const form_steps_1 = requireFormSteps();
		const misc_steps_1 = requireMiscSteps();
		const mock_steps_1 = requireMockSteps();
		const review_steps_1 = requireReviewSteps();
		exports.WizardUsers = ['ben', 'brandon', 'dimitri', 'jade', 'kira', 'locke', 'margot', 'ryan', 'scott', 'wally'];
		var AbortReason;
		(function (AbortReason) {
		    AbortReason["TooDifficult"] = "TOO_DIFFICULT";
		    AbortReason["BadInputs"] = "BAD_INPUTS";
		    AbortReason["GottaGo"] = "GOTTA_GO";
		    AbortReason["Other"] = "OTHER";
		    AbortReason["ClaimExpired"] = "CLAIM_EXPIRED";
		    AbortReason["UserCancelledPlan"] = "USER_CANCELLED_PLAN";
		    AbortReason["ShortCircuit"] = "SHORT_CIRCUIT";
		    AbortReason["SimplifiedFormShortCircuit"] = "sigmaModelEntryFormShortCircuit";
		    AbortReason["Unknown"] = "UNKNOWN";
		})(AbortReason || (exports.AbortReason = AbortReason = {}));
		exports.TestStepDefinitions = {
		    TestExtractSignals: test_extract_signals_1.TestExtractSignalsDefinition,
		    TestExtractPinout: test_extract_pinout_1.TestExtractPinoutDefinition,
		};
		exports.TestAndMockStepDefinitions = {
		    ...exports.TestStepDefinitions,
		    ...mock_steps_1.MockStepDefinitions,
		};
		exports.StepDefinitions = {
		    ...misc_steps_1.MiscStepDefinitions,
		    ...extract_steps_1.ExtractStepDefinitions,
		    ...review_steps_1.ReviewStepDefinitions,
		    ...form_steps_1.FormStepDefinitions,
		    ...exception_review_steps_1.ExceptionReviewStepDefinitions,
		    ...adjudication_steps_1.AdjudicationStepDefinitions,
		    ...exports.TestAndMockStepDefinitions,
		};
		exports.stepSchema = z.object({
		    id: z.string(),
		    type: z.string(),
		    input: z.record(z.string(), z.any()),
		    initialValue: z.record(z.string(), z.any()),
		});
		
	} (types));
	return types;
}

var PackageResolution = {};

var resolutionView = {};

var hasRequiredResolutionView;

function requireResolutionView () {
	if (hasRequiredResolutionView) return resolutionView;
	hasRequiredResolutionView = 1;
	Object.defineProperty(resolutionView, "__esModule", { value: true });
	resolutionView.BOUNDARY_CLASS_OC = resolutionView.BOUNDARY_CLASS_AMR = void 0;
	resolutionView.BOUNDARY_CLASS_AMR = 'ic.amr';
	resolutionView.BOUNDARY_CLASS_OC = 'ic.ec';
	
	return resolutionView;
}

var packageResolutionCodecs = {};

var hasRequiredPackageResolutionCodecs;

function requirePackageResolutionCodecs () {
	if (hasRequiredPackageResolutionCodecs) return packageResolutionCodecs;
	hasRequiredPackageResolutionCodecs = 1;
	Object.defineProperty(packageResolutionCodecs, "__esModule", { value: true });
	packageResolutionCodecs.resolutionToFormPayload = resolutionToFormPayload;
	packageResolutionCodecs.readFloatsAndTies = readFloatsAndTies;
	packageResolutionCodecs.readBoundariesByClass = readBoundariesByClass;
	packageResolutionCodecs.readStepValuesFromResolution = readStepValuesFromResolution;
	packageResolutionCodecs.buildPinsFromPinout = buildPinsFromPinout;
	packageResolutionCodecs.applyFloatsAndTies = applyFloatsAndTies;
	packageResolutionCodecs.buildParametersForResolution = buildParametersForResolution;
	packageResolutionCodecs.applyPayloadToResolution = applyPayloadToResolution;
	const CadstromObjectType_1 = requireCadstromObjectType();
	const CadstromUid_1 = requireCadstromUid$1();
	const resolution_projection_1 = requireResolutionProjection();
	const resolution_view_1 = requireResolutionView();
	function resolutionToFormPayload(sigmaModelType, resolution) {
	    return {
	        sigmaModelType,
	        signals: resolution.signals,
	        parameters: (0, resolution_projection_1.readParameters)(resolution.sigmaModelParameters),
	        pinout: (0, resolution_projection_1.readPinout)(resolution.pins),
	        spiceModels: resolution.spiceModels,
	        boundaries: resolution.boundaries,
	    };
	}
	function readFloatsAndTies(signals) {
	    return Object.fromEntries(signals.map((signal) => [signal.id, { floatingBehavior: signal.floatingBehavior, ties: signal.ties }]));
	}
	function readBoundariesByClass(boundaries, boundaryClass) {
	    return boundaries.filter((b) => b.boundary.class === boundaryClass);
	}
	function readStepValuesFromResolution(view) {
	    const signals = view.signals ?? [];
	    const boundaries = view.boundaries ?? [];
	    return {
	        signals,
	        pinout: (0, resolution_projection_1.readPinout)(view.pins ?? []),
	        parameters: (0, resolution_projection_1.readParameters)(view.sigmaModelParameters ?? {}),
	        floatsAndTies: readFloatsAndTies(signals),
	        functions: view.functions ?? [],
	        resources: view.resources ?? [],
	        modes: view.modes ?? [],
	        amrBoundaries: readBoundariesByClass(boundaries, resolution_view_1.BOUNDARY_CLASS_AMR),
	        ocBoundaries: readBoundariesByClass(boundaries, resolution_view_1.BOUNDARY_CLASS_OC),
	        spiceModels: view.spiceModels ?? [],
	    };
	}
	function buildPinsFromPinout(pinout) {
	    const pins = [];
	    for (const [signalId, pinNames] of Object.entries(pinout)) {
	        for (const pinName of pinNames) {
	            pins.push({
	                id: (0, CadstromUid_1.generateCadstromUid)(CadstromObjectType_1.CadstromObjectType.PackageStorePin),
	                name: pinName,
	                signalRef: signalId,
	            });
	        }
	    }
	    return pins;
	}
	function applyFloatsAndTies(signals, floatsAndTies) {
	    const updated = [...signals];
	    const indexById = new Map(updated.map((s, i) => [String(s.id), i]));
	    for (const [signalId, floatTie] of Object.entries(floatsAndTies)) {
	        const idx = indexById.get(signalId);
	        if (idx !== undefined) {
	            updated[idx] = { ...updated[idx], floatingBehavior: floatTie.floatingBehavior, ties: floatTie.ties };
	        }
	    }
	    return updated;
	}
	function buildParametersForResolution(parameters) {
	    const converted = {};
	    for (const [key, valueVariance] of Object.entries(parameters)) {
	        converted[key] = {
	            value: valueVariance.value ?? undefined,
	            variance: valueVariance.variance ?? undefined,
	        };
	    }
	    return converted;
	}
	function applyPayloadToResolution(payload) {
	    const pins = buildPinsFromPinout(payload.pinout);
	    const signals = applyFloatsAndTies(payload.signals, payload.floatsAndTies);
	    return {
	        sigmaModelId: payload.sigmaModelType,
	        pins,
	        sigmaModelParameters: buildParametersForResolution(payload.parameters),
	        modes: payload.modes,
	        boundaries: payload.boundaries,
	        resources: payload.resources,
	        functions: payload.functions,
	        signals,
	        spiceModels: payload.spiceModels,
	    };
	}
	
	return packageResolutionCodecs;
}

var resolutionBuilder = {};

var hasRequiredResolutionBuilder;

function requireResolutionBuilder () {
	if (hasRequiredResolutionBuilder) return resolutionBuilder;
	hasRequiredResolutionBuilder = 1;
	Object.defineProperty(resolutionBuilder, "__esModule", { value: true });
	resolutionBuilder.buildResolutionFromReview = buildResolutionFromReview;
	const sigma_engine_shared_1 = requireDist$1();
	const signal_id_orphan_pruning_1 = requireSignalIdOrphanPruning();
	const package_store_package_resolution_1 = requirePackageStorePackageResolution();
	const signal_tie_transforms_1 = requireSignalTieTransforms();
	const package_resolution_codecs_1 = requirePackageResolutionCodecs();
	function selectWrittenFields(view, writeFields) {
	    if (writeFields === undefined) {
	        return view;
	    }
	    const selected = {};
	    for (const field of writeFields) {
	        selected[field] = view[field];
	    }
	    return selected;
	}
	function buildResolutionFromReview(payload, opts) {
	    const view = (0, package_resolution_codecs_1.applyPayloadToResolution)(payload);
	    const written = selectWrittenFields({
	        ...view,
	        signals: (0, signal_tie_transforms_1.normalizeSignalTies)(view.signals),
	    }, opts?.writeFields);
	    const assembled = {
	        ...(opts?.base ?? (0, package_store_package_resolution_1.createDefaultPackageResolution)()),
	        ...written,
	    };
	    const signalPruned = pruneDeadSignalReferences(assembled, opts?.onOrphan);
	    if (opts?.modeProvenance === 'authored') {
	        return signalPruned;
	    }
	    return pruneDeadModeReferences(signalPruned, opts?.onOrphan);
	}
	function pruneDeadSignalReferences(resolution, onOrphan) {
	    const signalIds = new Set(resolution.signals.map((signal) => String(signal.id)));
	    const reporterFor = (field) => onOrphan ? (orphanId, fieldPath) => onOrphan(field, orphanId, fieldPath) : undefined;
	    return {
	        ...resolution,
	        signals: (0, signal_id_orphan_pruning_1.pruneTieOrphans)(resolution.signals, signalIds, reporterFor('ties')),
	        pins: (0, signal_id_orphan_pruning_1.prunePinOrphans)(resolution.pins, signalIds, reporterFor('pins')),
	        boundaries: (0, signal_id_orphan_pruning_1.pruneBoundaryOrphans)(resolution.boundaries, signalIds, reporterFor('boundaries')),
	        spiceModels: (0, signal_id_orphan_pruning_1.pruneSpiceModelOrphans)(resolution.spiceModels, signalIds, reporterFor('spiceModels')),
	    };
	}
	function pruneDeadModeReferences(resolution, onOrphan) {
	    const keptModes = (0, sigma_engine_shared_1.filterModesToReferencedOnly)(resolution.modes, resolution.signals, resolution.boundaries);
	    if (onOrphan !== undefined) {
	        const keptIds = new Set(keptModes.map((mode) => mode.id));
	        for (const mode of resolution.modes) {
	            if (!keptIds.has(mode.id)) {
	                onOrphan('modes', mode.id, 'unreferenced');
	            }
	        }
	    }
	    return { ...resolution, modes: keptModes };
	}
	
	return resolutionBuilder;
}

var hasRequiredPackageResolution;

function requirePackageResolution () {
	if (hasRequiredPackageResolution) return PackageResolution;
	hasRequiredPackageResolution = 1;
	(function (exports) {
		var __createBinding = (PackageResolution && PackageResolution.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (PackageResolution && PackageResolution.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requireResolutionView(), exports);
		__exportStar(requirePackageResolutionCodecs(), exports);
		__exportStar(requireResolutionBuilder(), exports);
		
	} (PackageResolution));
	return PackageResolution;
}

var generatorIds = {};

var hasRequiredGeneratorIds;

function requireGeneratorIds () {
	if (hasRequiredGeneratorIds) return generatorIds;
	hasRequiredGeneratorIds = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.WIZARD_SUPERVISOR_TASK_QUEUE = exports.CONSENSUS_ROLLUP_EXECUTION_TIMEOUT = exports.CONSENSUS_ROLLUP_WORKFLOW_TYPE = exports.PARALLEL_PU_SUPERVISOR_WORKFLOW_TYPE = exports.DEFAULT_PACKAGE_UNDERSTANDING_GENERATOR_ID = exports.IMPLEMENTATION_RULES_REVIEW_V1_GENERATOR_ID = exports.PACKAGE_DATA_ADJUDICATION_V1_GENERATOR_ID = exports.EXCEPTION_REVIEW_V1_GENERATOR_ID = exports.PARALLEL_PACKAGE_REVIEW_V1_GENERATOR_ID = exports.PACKAGE_REVIEW_V1_GENERATOR_ID = exports.PARALLEL_PACKAGE_UNDERSTANDING_V1_GENERATOR_ID = exports.PACKAGE_UNDERSTANDING_V1_GENERATOR_ID = void 0;
		exports.consensusRollupWorkflowId = consensusRollupWorkflowId;
		exports.PACKAGE_UNDERSTANDING_V1_GENERATOR_ID = 'PackageUnderstandingGeneratorV1';
		exports.PARALLEL_PACKAGE_UNDERSTANDING_V1_GENERATOR_ID = 'ParallelPackageUnderstandingGeneratorV1';
		exports.PACKAGE_REVIEW_V1_GENERATOR_ID = 'PackageReviewGeneratorV1';
		exports.PARALLEL_PACKAGE_REVIEW_V1_GENERATOR_ID = 'ParallelPackageReviewGeneratorV1';
		exports.EXCEPTION_REVIEW_V1_GENERATOR_ID = 'ExceptionReviewGeneratorV1';
		exports.PACKAGE_DATA_ADJUDICATION_V1_GENERATOR_ID = 'PackageDataAdjudicationGeneratorV1';
		exports.IMPLEMENTATION_RULES_REVIEW_V1_GENERATOR_ID = 'ImplementationRulesReviewGeneratorV1';
		exports.DEFAULT_PACKAGE_UNDERSTANDING_GENERATOR_ID = exports.PARALLEL_PACKAGE_REVIEW_V1_GENERATOR_ID;
		exports.PARALLEL_PU_SUPERVISOR_WORKFLOW_TYPE = 'ParallelPUSupervisor';
		exports.CONSENSUS_ROLLUP_WORKFLOW_TYPE = 'ConsensusRollup';
		exports.CONSENSUS_ROLLUP_EXECUTION_TIMEOUT = '1 hour';
		function consensusRollupWorkflowId(planId) {
		    return `consensus-rollup:${planId}`;
		}
		exports.WIZARD_SUPERVISOR_TASK_QUEUE = 'wizard-supervisor';
		
	} (generatorIds));
	return generatorIds;
}

var orgDefaults = {};

var hasRequiredOrgDefaults;

function requireOrgDefaults () {
	if (hasRequiredOrgDefaults) return orgDefaults;
	hasRequiredOrgDefaults = 1;
	Object.defineProperty(orgDefaults, "__esModule", { value: true });
	orgDefaults.DEFAULT_WIZARD_ORG_NAME = void 0;
	orgDefaults.DEFAULT_WIZARD_ORG_NAME = 'wizard_ops';
	
	return orgDefaults;
}

var packageReview = {};

var packageOperatorReviewStatus = {};

var hasRequiredPackageOperatorReviewStatus;

function requirePackageOperatorReviewStatus () {
	if (hasRequiredPackageOperatorReviewStatus) return packageOperatorReviewStatus;
	hasRequiredPackageOperatorReviewStatus = 1;
	Object.defineProperty(packageOperatorReviewStatus, "__esModule", { value: true });
	packageOperatorReviewStatus.packageOperatorReviewStatusSchema = packageOperatorReviewStatus.PackageOperatorReviewStatus = void 0;
	const z = /*@__PURE__*/ requireZod();
	var PackageOperatorReviewStatus;
	(function (PackageOperatorReviewStatus) {
	    PackageOperatorReviewStatus["Published"] = "published";
	    PackageOperatorReviewStatus["OperatorReviewComplete"] = "operatorReviewComplete";
	    PackageOperatorReviewStatus["AwaitingOperatorReview"] = "awaitingOperatorReview";
	    PackageOperatorReviewStatus["OperatorReviewFailed"] = "operatorReviewFailed";
	    PackageOperatorReviewStatus["OperatorReviewCancelled"] = "operatorReviewCancelled";
	    PackageOperatorReviewStatus["NotSubmittedForOperatorReview"] = "notSubmittedForOperatorReview";
	})(PackageOperatorReviewStatus || (packageOperatorReviewStatus.PackageOperatorReviewStatus = PackageOperatorReviewStatus = {}));
	packageOperatorReviewStatus.packageOperatorReviewStatusSchema = z.nativeEnum(PackageOperatorReviewStatus);
	
	return packageOperatorReviewStatus;
}

var hasRequiredPackageReview;

function requirePackageReview () {
	if (hasRequiredPackageReview) return packageReview;
	hasRequiredPackageReview = 1;
	Object.defineProperty(packageReview, "__esModule", { value: true });
	packageReview.packageReviewInputsSchema = void 0;
	packageReview.deriveReviewStatus = deriveReviewStatus;
	packageReview.summarizePackageReview = summarizePackageReview;
	const zod_1 = /*@__PURE__*/ requireZod();
	const package_operator_review_status_1 = requirePackageOperatorReviewStatus();
	const plan_status_1 = requirePlanStatus();
	packageReview.packageReviewInputsSchema = zod_1.z.object({
	    packageId: zod_1.z.string(),
	    isPublished: zod_1.z.boolean(),
	    operatorPlanStatus: zod_1.z.nativeEnum(plan_status_1.PlanStatus).nullable(),
	});
	function deriveReviewStatus({ isPublished, operatorPlanStatus, }) {
	    if (isPublished) {
	        return package_operator_review_status_1.PackageOperatorReviewStatus.Published;
	    }
	    switch (operatorPlanStatus) {
	        case null:
	            return package_operator_review_status_1.PackageOperatorReviewStatus.NotSubmittedForOperatorReview;
	        case plan_status_1.PlanStatus.Pending:
	            return package_operator_review_status_1.PackageOperatorReviewStatus.AwaitingOperatorReview;
	        case plan_status_1.PlanStatus.Complete:
	            return package_operator_review_status_1.PackageOperatorReviewStatus.OperatorReviewComplete;
	        case plan_status_1.PlanStatus.Failed:
	            return package_operator_review_status_1.PackageOperatorReviewStatus.OperatorReviewFailed;
	        case plan_status_1.PlanStatus.Cancelled:
	            return package_operator_review_status_1.PackageOperatorReviewStatus.OperatorReviewCancelled;
	        default: {
	            const exhaustiveCheck = operatorPlanStatus;
	            throw new Error(`Unhandled operator plan status: ${String(exhaustiveCheck)}`);
	        }
	    }
	}
	function summarizePackageReview(rows) {
	    const counts = Object.fromEntries(Object.values(package_operator_review_status_1.PackageOperatorReviewStatus).map((status) => [status, 0]));
	    for (const r of rows) {
	        counts[deriveReviewStatus(r)]++;
	    }
	    return counts;
	}
	
	return packageReview;
}

var planPriority = {};

var hasRequiredPlanPriority;

function requirePlanPriority () {
	if (hasRequiredPlanPriority) return planPriority;
	hasRequiredPlanPriority = 1;
	Object.defineProperty(planPriority, "__esModule", { value: true });
	planPriority.WIZARD_PLAN_PRIORITY = void 0;
	planPriority.WIZARD_PLAN_PRIORITY = {
	    manualPackageEditor: 2000,
	    datasheetCatalogueOnboarding: 1500,
	    packageDataAdjudication: 1750,
	    ingest: 1000,
	    cliDispatch: 1000,
	    dataScience: 100,
	};
	
	return planPriority;
}

var nonTestStepTypes = {};

var hasRequiredNonTestStepTypes;

function requireNonTestStepTypes () {
	if (hasRequiredNonTestStepTypes) return nonTestStepTypes;
	hasRequiredNonTestStepTypes = 1;
	Object.defineProperty(nonTestStepTypes, "__esModule", { value: true });
	nonTestStepTypes.nonTestStepTypes = void 0;
	const types_js_1 = requireTypes();
	nonTestStepTypes.nonTestStepTypes = Object.keys(types_js_1.StepDefinitions).filter((stepType) => !stepType.startsWith('Mock') && !stepType.startsWith('Test'));
	
	return nonTestStepTypes;
}

var operator = {};

var hasRequiredOperator;

function requireOperator () {
	if (hasRequiredOperator) return operator;
	hasRequiredOperator = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.eligiblePlansResultSchema = exports.activeClaimSummarySchema = exports.eligiblePlanSchema = exports.availableStepSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const admin_1 = requireAdmin();
		const types_1 = requireTypes();
		exports.availableStepSchema = zod_1.z.object({
		    step: types_1.stepSchema,
		    planId: zod_1.z.string(),
		    fragmentId: zod_1.z.string(),
		    deadlineAt: zod_1.z.number(),
		    priorityBucket: admin_1.priorityBucketSchema.nullable(),
		});
		exports.eligiblePlanSchema = zod_1.z.object({
		    planId: zod_1.z.string(),
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    manufacturerName: zod_1.z.string().nullable(),
		    generatorName: zod_1.z.string(),
		    priority: zod_1.z.number().nullable(),
		    createdAt: zod_1.z.number(),
		    deadlineAt: zod_1.z.number().nullable(),
		});
		exports.activeClaimSummarySchema = zod_1.z.object({
		    planId: zod_1.z.string(),
		    claimExpiresAt: zod_1.z.number(),
		});
		exports.eligiblePlansResultSchema = zod_1.z.object({
		    plans: exports.eligiblePlanSchema.array(),
		    activeClaim: exports.activeClaimSummarySchema.nullable(),
		});
		
	} (operator));
	return operator;
}

var operatorActivityReport = {};

var hasRequiredOperatorActivityReport;

function requireOperatorActivityReport () {
	if (hasRequiredOperatorActivityReport) return operatorActivityReport;
	hasRequiredOperatorActivityReport = 1;
	Object.defineProperty(operatorActivityReport, "__esModule", { value: true });
	operatorActivityReport.TraineeProgressSchema = operatorActivityReport.operatorActivityReportSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	const operatorThroughputDaySchema = zod_1.z.object({
	    day: zod_1.z.string(),
	    reviewInflow: zod_1.z.number(),
	    reviewOutflow: zod_1.z.number(),
	    reviewErrors: zod_1.z.number(),
	    reviewCancelled: zod_1.z.number(),
	    planInflow: zod_1.z.number(),
	    planOutflow: zod_1.z.number(),
	    planErrors: zod_1.z.number(),
	});
	const operatorBacklogDaySchema = zod_1.z.object({
	    day: zod_1.z.string(),
	    openReviews: zod_1.z.number(),
	    openPlans: zod_1.z.number(),
	});
	const operatorClaimItemSchema = zod_1.z.object({
	    userId: zod_1.z.string(),
	    displayName: zod_1.z.string(),
	    packageId: zod_1.z.string().nullable(),
	    mpn: zod_1.z.string().nullable(),
	    claimedAt: zod_1.z.number().nullable(),
	    ageMs: zod_1.z.number().nullable(),
	    expiresAt: zod_1.z.number(),
	    isStale: zod_1.z.boolean(),
	});
	const operatorClaimsSectionSchema = zod_1.z.object({
	    activeClaimedReviews: zod_1.z.number(),
	    byOperator: zod_1.z.array(zod_1.z.object({ userId: zod_1.z.string(), displayName: zod_1.z.string(), count: zod_1.z.number() })),
	    byPackage: zod_1.z.array(zod_1.z.object({ packageId: zod_1.z.string(), mpn: zod_1.z.string().nullable(), count: zod_1.z.number() })),
	    items: zod_1.z.array(operatorClaimItemSchema),
	});
	const operatorClaimableSectionSchema = zod_1.z.object({
	    totalClaimableReviewFragments: zod_1.z.number(),
	    perOperator: zod_1.z.array(zod_1.z.object({ userId: zod_1.z.string(), displayName: zod_1.z.string(), claimableDepth: zod_1.z.number() })),
	});
	operatorActivityReport.operatorActivityReportSchema = zod_1.z.object({
	    generatedAt: zod_1.z.string(),
	    lookbackDays: zod_1.z.number(),
	    throughput: zod_1.z.array(operatorThroughputDaySchema),
	    backlog: zod_1.z.array(operatorBacklogDaySchema),
	    claims: operatorClaimsSectionSchema,
	    claimable: operatorClaimableSectionSchema,
	});
	operatorActivityReport.TraineeProgressSchema = zod_1.z.object({
	    userId: zod_1.z.string(),
	    organizationId: zod_1.z.string(),
	    assigned: zod_1.z.number().int().nonnegative(),
	    completed: zod_1.z.number().int().nonnegative(),
	    lastActivityAt: zod_1.z.string().nullable(),
	});
	
	return operatorActivityReport;
}

var stepContext = {};

var hasRequiredStepContext;

function requireStepContext () {
	if (hasRequiredStepContext) return stepContext;
	hasRequiredStepContext = 1;
	Object.defineProperty(stepContext, "__esModule", { value: true });
	stepContext.wizardStepContextSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	stepContext.wizardStepContextSchema = zod_1.z.object({
	    planId: zod_1.z.string(),
	    packageId: zod_1.z.string().nullable(),
	    manufacturerPartNumber: zod_1.z.string().nullable(),
	    generatorName: zod_1.z.string(),
	});
	
	return stepContext;
}

var operatorEvalDto = {};

var hasRequiredOperatorEvalDto;

function requireOperatorEvalDto () {
	if (hasRequiredOperatorEvalDto) return operatorEvalDto;
	hasRequiredOperatorEvalDto = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.OperatorScorecardSchema = exports.HeatmapStatsSchema = exports.HeatmapRowSchema = exports.HeatmapCellSchema = exports.BootstrapCiSchema = exports.PairedTestResultSchema = exports.WinRateStatsSchema = exports.CohortCutoffsSchema = exports.CohortUserSummarySchema = exports.FailedEvalSummarySchema = exports.PlanEvalDetailSchema = exports.StepDetailSchema = exports.PlanEvalHistoryEntrySchema = exports.ScoredPlanSummarySchema = exports.OperatorSummarySchema = exports.PACKAGE_EVAL_PROPERTY_METRIC_KINDS = exports.PACKAGE_EVAL_PROPERTY_KEYS = exports.BOUNDARY_PROPERTY_KEYS = exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS = exports.DECOMPOSED_BOUNDARY_STEP_KEY = exports.OPERATOR_EVAL_STEP_METRIC_KINDS = void 0;
		exports.metricKindLabel = metricKindLabel;
		const zod_1 = /*@__PURE__*/ requireZod();
		const statistical_significance_rating_1 = requireStatisticalSignificanceRating();
		function metricKindLabel(kind) {
		    switch (kind) {
		        case 'f1':
		            return 'F1';
		        case 'setF1':
		            return 'Set F1';
		        case 'ted':
		            return 'TED';
		    }
		}
		exports.OPERATOR_EVAL_STEP_METRIC_KINDS = {
		    AssignSigmaModelType: 'f1',
		    ExtractBoundaries: 'setF1',
		    ExtractFunctions: 'ted',
		    ExtractModes: 'f1',
		    ExtractParameters: 'f1',
		    ExtractResources: 'f1',
		    ExtractSignalDriveModes: 'f1',
		    'ExtractSignals+ExtractPinout': 'f1',
		    GenerateImplementationRules: 'setF1',
		};
		exports.DECOMPOSED_BOUNDARY_STEP_KEY = 'ExtractBoundaries';
		exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS = {
		    'ExtractSignals+ExtractPinout': 'signals_pinout',
		    ExtractFunctions: 'functions',
		    ExtractModes: 'modes',
		    ExtractParameters: 'parameters',
		    ExtractResources: 'resources',
		    ExtractSignalDriveModes: 'drive_modes',
		    AssignSigmaModelType: 'sigma_model_type',
		    GenerateImplementationRules: 'implementation_rules',
		};
		exports.BOUNDARY_PROPERTY_KEYS = {
		    absoluteMaximumRatings: 'absolute_maximum_ratings',
		    operatingConditions: 'operating_conditions',
		};
		exports.PACKAGE_EVAL_PROPERTY_KEYS = [
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS['ExtractSignals+ExtractPinout'],
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.ExtractFunctions,
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.ExtractModes,
		    exports.BOUNDARY_PROPERTY_KEYS.absoluteMaximumRatings,
		    exports.BOUNDARY_PROPERTY_KEYS.operatingConditions,
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.ExtractParameters,
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.ExtractResources,
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.ExtractSignalDriveModes,
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.AssignSigmaModelType,
		    exports.OPERATOR_EVAL_STEP_PROPERTY_KEYS.GenerateImplementationRules,
		];
		exports.PACKAGE_EVAL_PROPERTY_METRIC_KINDS = {
		    signals_pinout: 'f1',
		    functions: 'ted',
		    modes: 'f1',
		    absolute_maximum_ratings: 'setF1',
		    operating_conditions: 'setF1',
		    parameters: 'f1',
		    resources: 'f1',
		    drive_modes: 'f1',
		    sigma_model_type: 'f1',
		    implementation_rules: 'setF1',
		};
		exports.OperatorSummarySchema = zod_1.z.object({
		    userId: zod_1.z.string(),
		    displayLabel: zod_1.z.string(),
		    completedReviewCount: zod_1.z.number().int().nonnegative(),
		});
		const OperatorRefSchema = zod_1.z.object({
		    userId: zod_1.z.string(),
		    displayLabel: zod_1.z.string(),
		});
		exports.ScoredPlanSummarySchema = zod_1.z.object({
		    reviewId: zod_1.z.string().nullable(),
		    planId: zod_1.z.string(),
		    completedAt: zod_1.z.string().nullable(),
		    evaluatedAt: zod_1.z.string(),
		    evalVersion: zod_1.z.string(),
		    groundTruthUpdatedAt: zod_1.z.string(),
		    aggregatePrimaryScore: zod_1.z.number(),
		    stepScores: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
		    packageId: zod_1.z.string(),
		    manufacturerPartNumber: zod_1.z.string(),
		    packageSigmaModelType: zod_1.z.string(),
		    opSigmaModelType: zod_1.z.string().nullable(),
		    operator: OperatorRefSchema.nullable(),
		    aiAggregatePrimaryScore: zod_1.z.number().nullable(),
		    aiStepScores: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
		    overallDelta: zod_1.z.number().nullable(),
		    stepDeltas: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
		});
		exports.PlanEvalHistoryEntrySchema = zod_1.z.object({
		    evaluatedAt: zod_1.z.string(),
		    evalVersion: zod_1.z.string(),
		    aggregatePrimaryScore: zod_1.z.number(),
		});
		const AttributionCountsSchema = zod_1.z.object({
		    improved: zod_1.z.number().int().nonnegative(),
		    regressed: zod_1.z.number().int().nonnegative(),
		    unchanged: zod_1.z.number().int().nonnegative(),
		    added: zod_1.z.number().int().nonnegative(),
		    removed: zod_1.z.number().int().nonnegative(),
		});
		exports.StepDetailSchema = zod_1.z.object({
		    taskName: zod_1.z.string(),
		    score: zod_1.z.number().nullable(),
		    groundTruth: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
		    inferred: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
		    aiInferred: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
		    attribution: AttributionCountsSchema.nullable(),
		});
		exports.PlanEvalDetailSchema = zod_1.z.object({
		    summary: exports.ScoredPlanSummarySchema,
		    history: zod_1.z.array(exports.PlanEvalHistoryEntrySchema),
		    steps: zod_1.z.array(exports.StepDetailSchema).default([]),
		});
		exports.FailedEvalSummarySchema = zod_1.z.object({
		    reviewId: zod_1.z.string().nullable(),
		    planId: zod_1.z.string(),
		    evalVersion: zod_1.z.string(),
		    errorClass: zod_1.z.string(),
		    errorMessage: zod_1.z.string(),
		    failedAt: zod_1.z.string(),
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    packageSigmaModelType: zod_1.z.string().nullable(),
		});
		const CohortPhaseSchema = zod_1.z.object({
		    total: zod_1.z.number().int(),
		    complete: zod_1.z.number().int(),
		    score: zod_1.z.number().nullable(),
		});
		exports.CohortUserSummarySchema = zod_1.z.object({
		    userId: zod_1.z.string(),
		    displayLabel: zod_1.z.string(),
		    cohort: zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(3)]),
		    phase1: CohortPhaseSchema,
		    phase2: CohortPhaseSchema.nullable(),
		});
		exports.CohortCutoffsSchema = zod_1.z
		    .object({ start: zod_1.z.string().optional(), end: zod_1.z.string().optional() })
		    .refine((v) => v.start !== undefined || v.end !== undefined, {
		    message: 'At least one of start or end must be provided',
		});
		exports.WinRateStatsSchema = zod_1.z.object({
		    winRate: zod_1.z.number(),
		    lossRate: zod_1.z.number(),
		    tieRate: zod_1.z.number(),
		    n: zod_1.z.number().int().nonnegative(),
		    ciLow: zod_1.z.number(),
		    ciHigh: zod_1.z.number(),
		});
		exports.PairedTestResultSchema = zod_1.z.object({
		    meanDelta: zod_1.z.number(),
		    stdDev: zod_1.z.number(),
		    n: zod_1.z.number().int().nonnegative(),
		    pValue: zod_1.z.number().nullable(),
		    significance: zod_1.z.nativeEnum(statistical_significance_rating_1.StatisticalSignificanceQualitativeRating).nullable(),
		});
		exports.BootstrapCiSchema = zod_1.z.object({
		    mean: zod_1.z.number(),
		    ciLow: zod_1.z.number(),
		    ciHigh: zod_1.z.number(),
		    n: zod_1.z.number().int().nonnegative(),
		});
		const OperatorScorecardStepSchema = zod_1.z.object({
		    meanDelta: zod_1.z.number().nullable(),
		    attributionTotals: AttributionCountsSchema,
		    pairedTest: exports.PairedTestResultSchema.nullable(),
		    fdrAdjustedPValue: zod_1.z.number().nullable(),
		    fdrAdjustedSignificance: zod_1.z.nativeEnum(statistical_significance_rating_1.StatisticalSignificanceQualitativeRating).nullable(),
		});
		exports.HeatmapCellSchema = zod_1.z.object({
		    op: zod_1.z.number().nullable(),
		    ai: zod_1.z.number().nullable(),
		    delta: zod_1.z.number().nullable(),
		    n: zod_1.z.number().int().nonnegative(),
		    regressionRate: zod_1.z.number(),
		    pValue: zod_1.z.number().nullable(),
		    significance: zod_1.z.nativeEnum(statistical_significance_rating_1.StatisticalSignificanceQualitativeRating).nullable(),
		});
		exports.HeatmapRowSchema = zod_1.z.object({
		    packageSigmaModelType: zod_1.z.string(),
		    reviewCount: zod_1.z.number().int().nonnegative(),
		    cells: zod_1.z.record(zod_1.z.string(), exports.HeatmapCellSchema),
		});
		exports.HeatmapStatsSchema = zod_1.z.object({
		    rows: zod_1.z.array(exports.HeatmapRowSchema),
		});
		exports.OperatorScorecardSchema = zod_1.z.object({
		    userId: zod_1.z.string(),
		    displayLabel: zod_1.z.string(),
		    reviewCount: zod_1.z.number().int().nonnegative(),
		    reviewsWhereOperatorBeatAi: zod_1.z.number().int().nonnegative(),
		    reviewsWhereOperatorLostToAi: zod_1.z.number().int().nonnegative(),
		    meanOverallDelta: zod_1.z.number().nullable(),
		    perStep: zod_1.z.record(zod_1.z.string(), OperatorScorecardStepSchema),
		    winRateStats: exports.WinRateStatsSchema.nullable(),
		    pairedTest: exports.PairedTestResultSchema.nullable(),
		    bootstrapCi: exports.BootstrapCiSchema.nullable(),
		});
		
	} (operatorEvalDto));
	return operatorEvalDto;
}

var wizardActivityPulse = {};

var hasRequiredWizardActivityPulse;

function requireWizardActivityPulse () {
	if (hasRequiredWizardActivityPulse) return wizardActivityPulse;
	hasRequiredWizardActivityPulse = 1;
	Object.defineProperty(wizardActivityPulse, "__esModule", { value: true });
	wizardActivityPulse.MAX_WIZARD_ACTIVITY_PULSE_ACTIVE_MS = wizardActivityPulse.WIZARD_ACTIVITY_PULSE_INTERVAL_MS = void 0;
	wizardActivityPulse.WIZARD_ACTIVITY_PULSE_INTERVAL_MS = 30_000;
	wizardActivityPulse.MAX_WIZARD_ACTIVITY_PULSE_ACTIVE_MS = 35_000;
	
	return wizardActivityPulse;
}

var wizardPlanActivities_types = {};

var hasRequiredWizardPlanActivities_types;

function requireWizardPlanActivities_types () {
	if (hasRequiredWizardPlanActivities_types) return wizardPlanActivities_types;
	hasRequiredWizardPlanActivities_types = 1;
	Object.defineProperty(wizardPlanActivities_types, "__esModule", { value: true });
	wizardPlanActivities_types.EnsurePackageUnderstandingPlanResultSchema = wizardPlanActivities_types.EnsurePackageUnderstandingPlanStatus = wizardPlanActivities_types.EnsurePackageUnderstandingPlanInputSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	wizardPlanActivities_types.EnsurePackageUnderstandingPlanInputSchema = zod_1.z.object({
	    packageId: zod_1.z.string(),
	    generatorName: zod_1.z.string(),
	    priority: zod_1.z.number().int().min(0).optional(),
	});
	var EnsurePackageUnderstandingPlanStatus;
	(function (EnsurePackageUnderstandingPlanStatus) {
	    EnsurePackageUnderstandingPlanStatus["Created"] = "created";
	    EnsurePackageUnderstandingPlanStatus["SkippedExisting"] = "skipped-existing";
	    EnsurePackageUnderstandingPlanStatus["SkippedOwned"] = "skipped-owned";
	    EnsurePackageUnderstandingPlanStatus["Failed"] = "failed";
	})(EnsurePackageUnderstandingPlanStatus || (wizardPlanActivities_types.EnsurePackageUnderstandingPlanStatus = EnsurePackageUnderstandingPlanStatus = {}));
	wizardPlanActivities_types.EnsurePackageUnderstandingPlanResultSchema = zod_1.z.object({
	    planId: zod_1.z.string().nullable(),
	    status: zod_1.z.nativeEnum(EnsurePackageUnderstandingPlanStatus),
	    errorMessage: zod_1.z.string().optional(),
	    retryable: zod_1.z.boolean().optional(),
	});
	
	return wizardPlanActivities_types;
}

var hasRequiredWizard;

function requireWizard () {
	if (hasRequiredWizard) return Wizard;
	hasRequiredWizard = 1;
	(function (exports) {
		var __createBinding = (Wizard && Wizard.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Wizard && Wizard.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PackageResolution = exports.SigmaModelEntryForm = void 0;
		__exportStar(requireAdmin(), exports);
		__exportStar(requirePlanStatus(), exports);
		__exportStar(requireAdminPlanFilterSchema(), exports);
		__exportStar(requireFragmentTypes(), exports);
		__exportStar(requireExtractFloatsAndTies_types(), exports);
		__exportStar(requireForm_types(), exports);
		__exportStar(requirePackageCommon(), exports);
		__exportStar(requireMisc(), exports);
		__exportStar(requireForm(), exports);
		__exportStar(requireOperatingConditionModes(), exports);
		__exportStar(requireNormalizedRatingsForm(), exports);
		__exportStar(requireNormalizedSignalDriveModesForm(), exports);
		exports.SigmaModelEntryForm = requireSigmaModelEntryForm();
		__exportStar(requireExceptionReview_types(), exports);
		__exportStar(requireExceptionReviewSteps(), exports);
		__exportStar(requireAdjudication(), exports);
		__exportStar(requireReview(), exports);
		__exportStar(requireTypes(), exports);
		exports.PackageResolution = requirePackageResolution();
		__exportStar(requireGeneratorIds(), exports);
		__exportStar(requireOrgDefaults(), exports);
		__exportStar(requirePackageReview(), exports);
		__exportStar(requirePlanPriority(), exports);
		__exportStar(requireNonTestStepTypes(), exports);
		__exportStar(requireOperator(), exports);
		__exportStar(requireOperatorActivityReport(), exports);
		__exportStar(requireStepContext(), exports);
		__exportStar(requireOperatorTimeLedger(), exports);
		__exportStar(requireOperatorEvalDto(), exports);
		__exportStar(requireWizardActivityPulse(), exports);
		__exportStar(requireWizardPlanActivities_types(), exports);
		
	} (Wizard));
	return Wizard;
}

var errors = {};

var packageOwnership_errors = {};

var hasRequiredPackageOwnership_errors;

function requirePackageOwnership_errors () {
	if (hasRequiredPackageOwnership_errors) return packageOwnership_errors;
	hasRequiredPackageOwnership_errors = 1;
	Object.defineProperty(packageOwnership_errors, "__esModule", { value: true });
	packageOwnership_errors.PackageFamilyOwnerMismatchError = packageOwnership_errors.NonGlobalPackageInGlobalFamilyError = packageOwnership_errors.FamilyChildOwnerConflictError = packageOwnership_errors.PackageOwnerImmutableError = packageOwnership_errors.FamilyAlreadyOwnedError = void 0;
	packageOwnership_errors.isFamilyAlreadyOwnedError = isFamilyAlreadyOwnedError;
	packageOwnership_errors.isFamilyChildOwnerConflictError = isFamilyChildOwnerConflictError;
	packageOwnership_errors.isPackageOwnerImmutableError = isPackageOwnerImmutableError;
	packageOwnership_errors.isNonGlobalPackageInGlobalFamilyError = isNonGlobalPackageInGlobalFamilyError;
	packageOwnership_errors.isPackageFamilyOwnerMismatchError = isPackageFamilyOwnerMismatchError;
	const domain_errors_1 = requireDomain_errors();
	class FamilyAlreadyOwnedError extends Error {
	    constructor(packageFamilyId, currentOwnerId, requestedOwnerId) {
	        super(`Package family ${packageFamilyId} is already owned by ${currentOwnerId}. ` +
	            'Use --force (CLI) to override (this is an admin-only action).');
	        this.name = this.constructor.name;
	        this.packageFamilyId = packageFamilyId;
	        this.currentOwnerId = currentOwnerId;
	        this.requestedOwnerId = requestedOwnerId;
	    }
	}
	packageOwnership_errors.FamilyAlreadyOwnedError = FamilyAlreadyOwnedError;
	class PackageOwnerImmutableError extends domain_errors_1.ValidationError {
	    constructor(entityKind, entityId, persistedOwnerId, incomingOwnerId) {
	        super(`Cannot change ownerId on ${entityKind} ${entityId} via update ` +
	            `(persisted=${persistedOwnerId}, incoming=${incomingOwnerId}). ` +
	            'Owner changes must go through packageFamily.assignOwner.');
	        this.entityKind = entityKind;
	        this.entityId = entityId;
	        this.persistedOwnerId = persistedOwnerId;
	        this.incomingOwnerId = incomingOwnerId;
	    }
	}
	packageOwnership_errors.PackageOwnerImmutableError = PackageOwnerImmutableError;
	class FamilyChildOwnerConflictError extends Error {
	    constructor(packageFamilyId, conflicts) {
	        super(`Force reassignment refused because ${conflicts.length} child package(s) of family ${packageFamilyId} ` +
	            'have third-party owners. Use --overwrite-conflicts to override.');
	        this.name = this.constructor.name;
	        this.packageFamilyId = packageFamilyId;
	        this.conflicts = conflicts;
	    }
	}
	packageOwnership_errors.FamilyChildOwnerConflictError = FamilyChildOwnerConflictError;
	class NonGlobalPackageInGlobalFamilyError extends Error {
	    constructor(packageFamilyId, requestedOwnerId) {
	        super(`Package family ${packageFamilyId} is globally shared, so a package owned by ` +
	            `${requestedOwnerId} cannot be created in it. Choose the Global owner, or create ` +
	            'the package in an organization-owned family.');
	        this.name = this.constructor.name;
	        this.packageFamilyId = packageFamilyId;
	        this.requestedOwnerId = requestedOwnerId;
	    }
	}
	packageOwnership_errors.NonGlobalPackageInGlobalFamilyError = NonGlobalPackageInGlobalFamilyError;
	class PackageFamilyOwnerMismatchError extends Error {
	    constructor(packageFamilyId, requestedOwnerId, familyOwnerId) {
	        super(`Package family ${packageFamilyId} is owned by ${familyOwnerId}, so a package owned by ` +
	            `${requestedOwnerId} cannot be created in it. Packages inherit their family's owner; ` +
	            'create the package in a family owned by the requested organization.');
	        this.name = this.constructor.name;
	        this.packageFamilyId = packageFamilyId;
	        this.requestedOwnerId = requestedOwnerId;
	        this.familyOwnerId = familyOwnerId;
	    }
	}
	packageOwnership_errors.PackageFamilyOwnerMismatchError = PackageFamilyOwnerMismatchError;
	function isFamilyAlreadyOwnedError(error) {
	    return error instanceof FamilyAlreadyOwnedError;
	}
	function isFamilyChildOwnerConflictError(error) {
	    return error instanceof FamilyChildOwnerConflictError;
	}
	function isPackageOwnerImmutableError(error) {
	    return error instanceof PackageOwnerImmutableError;
	}
	function isNonGlobalPackageInGlobalFamilyError(error) {
	    return error instanceof NonGlobalPackageInGlobalFamilyError;
	}
	function isPackageFamilyOwnerMismatchError(error) {
	    return error instanceof PackageFamilyOwnerMismatchError;
	}
	
	return packageOwnership_errors;
}

var package_errors = {};

var hasRequiredPackage_errors;

function requirePackage_errors () {
	if (hasRequiredPackage_errors) return package_errors;
	hasRequiredPackage_errors = 1;
	Object.defineProperty(package_errors, "__esModule", { value: true });
	package_errors.InvalidDatasheetFileError = package_errors.DatasheetFileOwnerMismatchError = package_errors.DatasheetDownloadFailedError = package_errors.DatasheetFileNotFoundError = package_errors.PackageFamilyPrefixMismatchError = package_errors.PackageAlreadyExistsError = package_errors.PackageFamilyNotFoundError = void 0;
	package_errors.isPackageFamilyNotFoundError = isPackageFamilyNotFoundError;
	package_errors.isPackageAlreadyExistsError = isPackageAlreadyExistsError;
	package_errors.isPackageFamilyPrefixMismatchError = isPackageFamilyPrefixMismatchError;
	package_errors.isDatasheetFileNotFoundError = isDatasheetFileNotFoundError;
	package_errors.isDatasheetDownloadFailedError = isDatasheetDownloadFailedError;
	package_errors.isDatasheetFileOwnerMismatchError = isDatasheetFileOwnerMismatchError;
	package_errors.isInvalidDatasheetFileError = isInvalidDatasheetFileError;
	class PackageFamilyNotFoundError extends Error {
	    constructor(packageFamilyId) {
	        super(`PackageFamily not found: ${packageFamilyId}`);
	        this.name = this.constructor.name;
	        this.packageFamilyId = packageFamilyId;
	    }
	}
	package_errors.PackageFamilyNotFoundError = PackageFamilyNotFoundError;
	function isPackageFamilyNotFoundError(error) {
	    return error instanceof PackageFamilyNotFoundError;
	}
	class PackageAlreadyExistsError extends Error {
	    constructor(args) {
	        super(`A package with manufacturerPartNumber='${args.manufacturerPartNumber}', ` +
	            `variantSuffix='${args.variantSuffix}', manufacturerId='${args.manufacturerId}', ` +
	            `ownerId='${args.ownerId}' already exists. To create a new package, change one of: ` +
	            `manufacturerPartNumber, variantSuffix, manufacturerName, or owner.`);
	        this.name = this.constructor.name;
	        this.manufacturerPartNumber = args.manufacturerPartNumber;
	        this.variantSuffix = args.variantSuffix;
	        this.manufacturerId = args.manufacturerId;
	        this.ownerId = args.ownerId;
	    }
	}
	package_errors.PackageAlreadyExistsError = PackageAlreadyExistsError;
	function isPackageAlreadyExistsError(error) {
	    return error instanceof PackageAlreadyExistsError;
	}
	class PackageFamilyPrefixMismatchError extends Error {
	    constructor(args) {
	        super(`PackageFamily '${args.familyName}' (id='${args.packageFamilyId}') is not a ` +
	            `case-insensitive prefix of MPN '${args.mpn}'.`);
	        this.name = this.constructor.name;
	        this.packageFamilyId = args.packageFamilyId;
	        this.familyName = args.familyName;
	        this.mpn = args.mpn;
	    }
	}
	package_errors.PackageFamilyPrefixMismatchError = PackageFamilyPrefixMismatchError;
	function isPackageFamilyPrefixMismatchError(error) {
	    return error instanceof PackageFamilyPrefixMismatchError;
	}
	class DatasheetFileNotFoundError extends Error {
	    constructor(fileId) {
	        super(`Datasheet file not found: ${fileId}`);
	        this.name = this.constructor.name;
	        this.fileId = fileId;
	    }
	}
	package_errors.DatasheetFileNotFoundError = DatasheetFileNotFoundError;
	function isDatasheetFileNotFoundError(error) {
	    return error instanceof DatasheetFileNotFoundError;
	}
	class DatasheetDownloadFailedError extends Error {
	    constructor(url) {
	        super(`Could not download a datasheet from ${url}. The site may be blocking automated downloads — try uploading the file directly instead.`);
	        this.name = this.constructor.name;
	        this.url = url;
	    }
	}
	package_errors.DatasheetDownloadFailedError = DatasheetDownloadFailedError;
	function isDatasheetDownloadFailedError(error) {
	    return error instanceof DatasheetDownloadFailedError;
	}
	class DatasheetFileOwnerMismatchError extends Error {
	    constructor(args) {
	        const packageOwnerDescription = args.packageOwnerId === null ? 'publicly owned' : `owned by '${args.packageOwnerId}'`;
	        const packageDescription = args.packageId === null ? 'the candidate package' : `package '${args.packageId}'`;
	        super(`File '${args.fileId}' is owned by '${args.fileOwnerId}' but ${packageDescription} is ` +
	            `${packageOwnerDescription}. Pass a public (non-owned) file, or one owned by the same tenant.`);
	        this.name = this.constructor.name;
	        this.fileId = args.fileId;
	        this.fileOwnerId = args.fileOwnerId;
	        this.packageId = args.packageId;
	        this.packageOwnerId = args.packageOwnerId;
	    }
	}
	package_errors.DatasheetFileOwnerMismatchError = DatasheetFileOwnerMismatchError;
	function isDatasheetFileOwnerMismatchError(error) {
	    return error instanceof DatasheetFileOwnerMismatchError;
	}
	class InvalidDatasheetFileError extends Error {
	    constructor(fileName) {
	        super(`The file "${fileName}" is not a valid PDF. Please upload a PDF datasheet.`);
	        this.name = this.constructor.name;
	        this.fileName = fileName;
	    }
	}
	package_errors.InvalidDatasheetFileError = InvalidDatasheetFileError;
	function isInvalidDatasheetFileError(error) {
	    return error instanceof InvalidDatasheetFileError;
	}
	
	return package_errors;
}

var packageDataSpec_errors = {};

var hasRequiredPackageDataSpec_errors;

function requirePackageDataSpec_errors () {
	if (hasRequiredPackageDataSpec_errors) return packageDataSpec_errors;
	hasRequiredPackageDataSpec_errors = 1;
	Object.defineProperty(packageDataSpec_errors, "__esModule", { value: true });
	packageDataSpec_errors.InferenceErrorCodeRetiredError = packageDataSpec_errors.InferenceErrorCodeAlreadyExistsError = packageDataSpec_errors.InferenceErrorCodeNotFoundError = packageDataSpec_errors.SpecClauseSelfReplacementError = packageDataSpec_errors.SpecClauseReplacementRetiredError = packageDataSpec_errors.SpecClauseAlreadyReplacedError = packageDataSpec_errors.SpecClauseNotRetiredError = packageDataSpec_errors.SpecClauseShortNameConflictError = packageDataSpec_errors.SpecClauseSupersededError = packageDataSpec_errors.SpecClauseRetiredError = packageDataSpec_errors.SpecClauseVersionConflictError = packageDataSpec_errors.SpecClauseNotFoundError = void 0;
	packageDataSpec_errors.isSpecClauseNotFoundError = isSpecClauseNotFoundError;
	packageDataSpec_errors.isSpecClauseVersionConflictError = isSpecClauseVersionConflictError;
	packageDataSpec_errors.isSpecClauseRetiredError = isSpecClauseRetiredError;
	packageDataSpec_errors.isSpecClauseSupersededError = isSpecClauseSupersededError;
	packageDataSpec_errors.isSpecClauseShortNameConflictError = isSpecClauseShortNameConflictError;
	packageDataSpec_errors.isSpecClauseNotRetiredError = isSpecClauseNotRetiredError;
	packageDataSpec_errors.isSpecClauseAlreadyReplacedError = isSpecClauseAlreadyReplacedError;
	packageDataSpec_errors.isSpecClauseReplacementRetiredError = isSpecClauseReplacementRetiredError;
	packageDataSpec_errors.isSpecClauseSelfReplacementError = isSpecClauseSelfReplacementError;
	packageDataSpec_errors.isInferenceErrorCodeNotFoundError = isInferenceErrorCodeNotFoundError;
	packageDataSpec_errors.isInferenceErrorCodeAlreadyExistsError = isInferenceErrorCodeAlreadyExistsError;
	packageDataSpec_errors.isInferenceErrorCodeRetiredError = isInferenceErrorCodeRetiredError;
	class SpecClauseNotFoundError extends Error {
	    constructor(clauseId) {
	        super(`Specification clause not found: ${clauseId}`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseNotFoundError = SpecClauseNotFoundError;
	function isSpecClauseNotFoundError(error) {
	    return error instanceof SpecClauseNotFoundError;
	}
	class SpecClauseVersionConflictError extends Error {
	    constructor(clauseId) {
	        super(`Clause ${clauseId} is no longer the current version of its clause. ` +
	            `Someone else edited it — reload the clause and try again.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseVersionConflictError = SpecClauseVersionConflictError;
	function isSpecClauseVersionConflictError(error) {
	    return error instanceof SpecClauseVersionConflictError;
	}
	class SpecClauseRetiredError extends Error {
	    constructor(clauseId) {
	        super(`Clause ${clauseId} is retired and cannot be edited. Reinstate it first, or write a new clause.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseRetiredError = SpecClauseRetiredError;
	function isSpecClauseRetiredError(error) {
	    return error instanceof SpecClauseRetiredError;
	}
	class SpecClauseSupersededError extends Error {
	    constructor(clauseId) {
	        super(`Clause ${clauseId} was replaced by another clause and cannot be reinstated. ` +
	            `Retire the replacement first if this clause should apply again.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseSupersededError = SpecClauseSupersededError;
	function isSpecClauseSupersededError(error) {
	    return error instanceof SpecClauseSupersededError;
	}
	class SpecClauseShortNameConflictError extends Error {
	    constructor(args) {
	        super(`The name '${args.shortName}' is already used by clause #${args.conflictingClauseNumber} ` +
	            `under '${args.propertyKey}'. Choose a different name.`);
	        this.name = this.constructor.name;
	        this.propertyKey = args.propertyKey;
	        this.shortName = args.shortName;
	        this.conflictingClauseNumber = args.conflictingClauseNumber;
	    }
	}
	packageDataSpec_errors.SpecClauseShortNameConflictError = SpecClauseShortNameConflictError;
	function isSpecClauseShortNameConflictError(error) {
	    return error instanceof SpecClauseShortNameConflictError;
	}
	class SpecClauseNotRetiredError extends Error {
	    constructor(clauseId) {
	        super(`Clause ${clauseId} is still active. Supersede it instead of recording a replacement.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseNotRetiredError = SpecClauseNotRetiredError;
	function isSpecClauseNotRetiredError(error) {
	    return error instanceof SpecClauseNotRetiredError;
	}
	class SpecClauseAlreadyReplacedError extends Error {
	    constructor(clauseId) {
	        super(`Clause ${clauseId} already has a replacement. Reload the clauses to see it.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseAlreadyReplacedError = SpecClauseAlreadyReplacedError;
	function isSpecClauseAlreadyReplacedError(error) {
	    return error instanceof SpecClauseAlreadyReplacedError;
	}
	class SpecClauseReplacementRetiredError extends Error {
	    constructor(replacementClauseId) {
	        super(`Clause ${replacementClauseId} is retired and cannot replace another clause. Choose an active clause.`);
	        this.name = this.constructor.name;
	        this.replacementClauseId = replacementClauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseReplacementRetiredError = SpecClauseReplacementRetiredError;
	function isSpecClauseReplacementRetiredError(error) {
	    return error instanceof SpecClauseReplacementRetiredError;
	}
	class SpecClauseSelfReplacementError extends Error {
	    constructor(clauseId) {
	        super(`Clause ${clauseId} cannot replace itself. Choose a different clause.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	    }
	}
	packageDataSpec_errors.SpecClauseSelfReplacementError = SpecClauseSelfReplacementError;
	function isSpecClauseSelfReplacementError(error) {
	    return error instanceof SpecClauseSelfReplacementError;
	}
	class InferenceErrorCodeNotFoundError extends Error {
	    constructor(errorCodeId) {
	        super(`Inference error code not found: ${errorCodeId}`);
	        this.name = this.constructor.name;
	        this.errorCodeId = errorCodeId;
	    }
	}
	packageDataSpec_errors.InferenceErrorCodeNotFoundError = InferenceErrorCodeNotFoundError;
	function isInferenceErrorCodeNotFoundError(error) {
	    return error instanceof InferenceErrorCodeNotFoundError;
	}
	class InferenceErrorCodeAlreadyExistsError extends Error {
	    constructor(args) {
	        super(`The error code '${args.code}' already exists under '${args.propertyKey}'. ` +
	            `Pick a different code, or reinstate the existing one if it was retired.`);
	        this.name = this.constructor.name;
	        this.propertyKey = args.propertyKey;
	        this.code = args.code;
	    }
	}
	packageDataSpec_errors.InferenceErrorCodeAlreadyExistsError = InferenceErrorCodeAlreadyExistsError;
	function isInferenceErrorCodeAlreadyExistsError(error) {
	    return error instanceof InferenceErrorCodeAlreadyExistsError;
	}
	class InferenceErrorCodeRetiredError extends Error {
	    constructor(errorCodeId) {
	        super(`Error code ${errorCodeId} is retired and cannot be edited. Reinstate it first.`);
	        this.name = this.constructor.name;
	        this.errorCodeId = errorCodeId;
	    }
	}
	packageDataSpec_errors.InferenceErrorCodeRetiredError = InferenceErrorCodeRetiredError;
	function isInferenceErrorCodeRetiredError(error) {
	    return error instanceof InferenceErrorCodeRetiredError;
	}
	
	return packageDataSpec_errors;
}

var packageDataAdjudication_errors = {};

var hasRequiredPackageDataAdjudication_errors;

function requirePackageDataAdjudication_errors () {
	if (hasRequiredPackageDataAdjudication_errors) return packageDataAdjudication_errors;
	hasRequiredPackageDataAdjudication_errors = 1;
	Object.defineProperty(packageDataAdjudication_errors, "__esModule", { value: true });
	packageDataAdjudication_errors.ErrorCodeCitationPropertyMismatchError = packageDataAdjudication_errors.ClauseCitationPropertyMismatchError = void 0;
	packageDataAdjudication_errors.isClauseCitationPropertyMismatchError = isClauseCitationPropertyMismatchError;
	packageDataAdjudication_errors.isErrorCodeCitationPropertyMismatchError = isErrorCodeCitationPropertyMismatchError;
	class ClauseCitationPropertyMismatchError extends Error {
	    constructor(clauseId, clausePropertyKey, addressPropertyKey) {
	        super(`Clause ${clauseId} governs '${clausePropertyKey}' and cannot be cited on ` +
	            `'${addressPropertyKey}' data. Pick a clause from the property being reviewed.`);
	        this.name = this.constructor.name;
	        this.clauseId = clauseId;
	        this.clausePropertyKey = clausePropertyKey;
	        this.addressPropertyKey = addressPropertyKey;
	    }
	}
	packageDataAdjudication_errors.ClauseCitationPropertyMismatchError = ClauseCitationPropertyMismatchError;
	function isClauseCitationPropertyMismatchError(error) {
	    return error instanceof ClauseCitationPropertyMismatchError;
	}
	class ErrorCodeCitationPropertyMismatchError extends Error {
	    constructor(errorCodeId, errorCodePropertyKey, addressPropertyKey) {
	        super(`Error code ${errorCodeId} belongs to '${errorCodePropertyKey}' and cannot tag ` +
	            `'${addressPropertyKey}' data. Pick a code from the property being reviewed.`);
	        this.name = this.constructor.name;
	        this.errorCodeId = errorCodeId;
	        this.errorCodePropertyKey = errorCodePropertyKey;
	        this.addressPropertyKey = addressPropertyKey;
	    }
	}
	packageDataAdjudication_errors.ErrorCodeCitationPropertyMismatchError = ErrorCodeCitationPropertyMismatchError;
	function isErrorCodeCitationPropertyMismatchError(error) {
	    return error instanceof ErrorCodeCitationPropertyMismatchError;
	}
	
	return packageDataAdjudication_errors;
}

var projectModel_errors = {};

var hasRequiredProjectModel_errors;

function requireProjectModel_errors () {
	if (hasRequiredProjectModel_errors) return projectModel_errors;
	hasRequiredProjectModel_errors = 1;
	Object.defineProperty(projectModel_errors, "__esModule", { value: true });
	projectModel_errors.ModuleInstanceNotFoundError = void 0;
	projectModel_errors.isModuleInstanceNotFoundError = isModuleInstanceNotFoundError;
	class ModuleInstanceNotFoundError extends Error {
	    constructor(refOrName, hint) {
	        super(`project_module not found: ${refOrName}`);
	        this.name = this.constructor.name;
	        this.refOrName = refOrName;
	        this.hint = hint;
	    }
	}
	projectModel_errors.ModuleInstanceNotFoundError = ModuleInstanceNotFoundError;
	function isModuleInstanceNotFoundError(error) {
	    return error instanceof ModuleInstanceNotFoundError;
	}
	
	return projectModel_errors;
}

var validation_error = {};

var hasRequiredValidation_error;

function requireValidation_error () {
	if (hasRequiredValidation_error) return validation_error;
	hasRequiredValidation_error = 1;
	Object.defineProperty(validation_error, "__esModule", { value: true });
	validation_error.ValidationError = void 0;
	validation_error.isValidationError = isValidationError;
	class ValidationError extends Error {
	    constructor(message) {
	        super(message);
	        this.name = this.constructor.name;
	    }
	}
	validation_error.ValidationError = ValidationError;
	function isValidationError(error) {
	    return error instanceof ValidationError;
	}
	
	return validation_error;
}

var hasRequiredErrors;

function requireErrors () {
	if (hasRequiredErrors) return errors;
	hasRequiredErrors = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.isClauseCitationPropertyMismatchError = exports.ClauseCitationPropertyMismatchError = exports.isInferenceErrorCodeRetiredError = exports.InferenceErrorCodeRetiredError = exports.isInferenceErrorCodeAlreadyExistsError = exports.InferenceErrorCodeAlreadyExistsError = exports.isInferenceErrorCodeNotFoundError = exports.InferenceErrorCodeNotFoundError = exports.isSpecClauseSelfReplacementError = exports.SpecClauseSelfReplacementError = exports.isSpecClauseReplacementRetiredError = exports.SpecClauseReplacementRetiredError = exports.isSpecClauseAlreadyReplacedError = exports.SpecClauseAlreadyReplacedError = exports.isSpecClauseNotRetiredError = exports.SpecClauseNotRetiredError = exports.isSpecClauseShortNameConflictError = exports.SpecClauseShortNameConflictError = exports.isSpecClauseSupersededError = exports.SpecClauseSupersededError = exports.isSpecClauseRetiredError = exports.SpecClauseRetiredError = exports.isSpecClauseVersionConflictError = exports.SpecClauseVersionConflictError = exports.isSpecClauseNotFoundError = exports.SpecClauseNotFoundError = exports.isInvalidDatasheetFileError = exports.InvalidDatasheetFileError = exports.isDatasheetFileOwnerMismatchError = exports.DatasheetFileOwnerMismatchError = exports.isDatasheetDownloadFailedError = exports.DatasheetDownloadFailedError = exports.isDatasheetFileNotFoundError = exports.DatasheetFileNotFoundError = exports.isPackageFamilyPrefixMismatchError = exports.PackageFamilyPrefixMismatchError = exports.isPackageAlreadyExistsError = exports.PackageAlreadyExistsError = exports.isPackageFamilyNotFoundError = exports.PackageFamilyNotFoundError = exports.isPackageFamilyOwnerMismatchError = exports.isNonGlobalPackageInGlobalFamilyError = exports.isFamilyChildOwnerConflictError = exports.isFamilyAlreadyOwnedError = exports.isPackageOwnerImmutableError = exports.PackageFamilyOwnerMismatchError = exports.NonGlobalPackageInGlobalFamilyError = exports.FamilyChildOwnerConflictError = exports.FamilyAlreadyOwnedError = exports.PackageOwnerImmutableError = void 0;
		exports.isValidationError = exports.ValidationError = exports.isModuleInstanceNotFoundError = exports.ModuleInstanceNotFoundError = exports.isErrorCodeCitationPropertyMismatchError = exports.ErrorCodeCitationPropertyMismatchError = void 0;
		var package_ownership_errors_1 = requirePackageOwnership_errors();
		Object.defineProperty(exports, "PackageOwnerImmutableError", { enumerable: true, get: function () { return package_ownership_errors_1.PackageOwnerImmutableError; } });
		Object.defineProperty(exports, "FamilyAlreadyOwnedError", { enumerable: true, get: function () { return package_ownership_errors_1.FamilyAlreadyOwnedError; } });
		Object.defineProperty(exports, "FamilyChildOwnerConflictError", { enumerable: true, get: function () { return package_ownership_errors_1.FamilyChildOwnerConflictError; } });
		Object.defineProperty(exports, "NonGlobalPackageInGlobalFamilyError", { enumerable: true, get: function () { return package_ownership_errors_1.NonGlobalPackageInGlobalFamilyError; } });
		Object.defineProperty(exports, "PackageFamilyOwnerMismatchError", { enumerable: true, get: function () { return package_ownership_errors_1.PackageFamilyOwnerMismatchError; } });
		Object.defineProperty(exports, "isPackageOwnerImmutableError", { enumerable: true, get: function () { return package_ownership_errors_1.isPackageOwnerImmutableError; } });
		Object.defineProperty(exports, "isFamilyAlreadyOwnedError", { enumerable: true, get: function () { return package_ownership_errors_1.isFamilyAlreadyOwnedError; } });
		Object.defineProperty(exports, "isFamilyChildOwnerConflictError", { enumerable: true, get: function () { return package_ownership_errors_1.isFamilyChildOwnerConflictError; } });
		Object.defineProperty(exports, "isNonGlobalPackageInGlobalFamilyError", { enumerable: true, get: function () { return package_ownership_errors_1.isNonGlobalPackageInGlobalFamilyError; } });
		Object.defineProperty(exports, "isPackageFamilyOwnerMismatchError", { enumerable: true, get: function () { return package_ownership_errors_1.isPackageFamilyOwnerMismatchError; } });
		var package_errors_1 = requirePackage_errors();
		Object.defineProperty(exports, "PackageFamilyNotFoundError", { enumerable: true, get: function () { return package_errors_1.PackageFamilyNotFoundError; } });
		Object.defineProperty(exports, "isPackageFamilyNotFoundError", { enumerable: true, get: function () { return package_errors_1.isPackageFamilyNotFoundError; } });
		Object.defineProperty(exports, "PackageAlreadyExistsError", { enumerable: true, get: function () { return package_errors_1.PackageAlreadyExistsError; } });
		Object.defineProperty(exports, "isPackageAlreadyExistsError", { enumerable: true, get: function () { return package_errors_1.isPackageAlreadyExistsError; } });
		Object.defineProperty(exports, "PackageFamilyPrefixMismatchError", { enumerable: true, get: function () { return package_errors_1.PackageFamilyPrefixMismatchError; } });
		Object.defineProperty(exports, "isPackageFamilyPrefixMismatchError", { enumerable: true, get: function () { return package_errors_1.isPackageFamilyPrefixMismatchError; } });
		Object.defineProperty(exports, "DatasheetFileNotFoundError", { enumerable: true, get: function () { return package_errors_1.DatasheetFileNotFoundError; } });
		Object.defineProperty(exports, "isDatasheetFileNotFoundError", { enumerable: true, get: function () { return package_errors_1.isDatasheetFileNotFoundError; } });
		Object.defineProperty(exports, "DatasheetDownloadFailedError", { enumerable: true, get: function () { return package_errors_1.DatasheetDownloadFailedError; } });
		Object.defineProperty(exports, "isDatasheetDownloadFailedError", { enumerable: true, get: function () { return package_errors_1.isDatasheetDownloadFailedError; } });
		Object.defineProperty(exports, "DatasheetFileOwnerMismatchError", { enumerable: true, get: function () { return package_errors_1.DatasheetFileOwnerMismatchError; } });
		Object.defineProperty(exports, "isDatasheetFileOwnerMismatchError", { enumerable: true, get: function () { return package_errors_1.isDatasheetFileOwnerMismatchError; } });
		Object.defineProperty(exports, "InvalidDatasheetFileError", { enumerable: true, get: function () { return package_errors_1.InvalidDatasheetFileError; } });
		Object.defineProperty(exports, "isInvalidDatasheetFileError", { enumerable: true, get: function () { return package_errors_1.isInvalidDatasheetFileError; } });
		var package_data_spec_errors_1 = requirePackageDataSpec_errors();
		Object.defineProperty(exports, "SpecClauseNotFoundError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseNotFoundError; } });
		Object.defineProperty(exports, "isSpecClauseNotFoundError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseNotFoundError; } });
		Object.defineProperty(exports, "SpecClauseVersionConflictError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseVersionConflictError; } });
		Object.defineProperty(exports, "isSpecClauseVersionConflictError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseVersionConflictError; } });
		Object.defineProperty(exports, "SpecClauseRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseRetiredError; } });
		Object.defineProperty(exports, "isSpecClauseRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseRetiredError; } });
		Object.defineProperty(exports, "SpecClauseSupersededError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseSupersededError; } });
		Object.defineProperty(exports, "isSpecClauseSupersededError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseSupersededError; } });
		Object.defineProperty(exports, "SpecClauseShortNameConflictError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseShortNameConflictError; } });
		Object.defineProperty(exports, "isSpecClauseShortNameConflictError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseShortNameConflictError; } });
		Object.defineProperty(exports, "SpecClauseNotRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseNotRetiredError; } });
		Object.defineProperty(exports, "isSpecClauseNotRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseNotRetiredError; } });
		Object.defineProperty(exports, "SpecClauseAlreadyReplacedError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseAlreadyReplacedError; } });
		Object.defineProperty(exports, "isSpecClauseAlreadyReplacedError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseAlreadyReplacedError; } });
		Object.defineProperty(exports, "SpecClauseReplacementRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseReplacementRetiredError; } });
		Object.defineProperty(exports, "isSpecClauseReplacementRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseReplacementRetiredError; } });
		Object.defineProperty(exports, "SpecClauseSelfReplacementError", { enumerable: true, get: function () { return package_data_spec_errors_1.SpecClauseSelfReplacementError; } });
		Object.defineProperty(exports, "isSpecClauseSelfReplacementError", { enumerable: true, get: function () { return package_data_spec_errors_1.isSpecClauseSelfReplacementError; } });
		Object.defineProperty(exports, "InferenceErrorCodeNotFoundError", { enumerable: true, get: function () { return package_data_spec_errors_1.InferenceErrorCodeNotFoundError; } });
		Object.defineProperty(exports, "isInferenceErrorCodeNotFoundError", { enumerable: true, get: function () { return package_data_spec_errors_1.isInferenceErrorCodeNotFoundError; } });
		Object.defineProperty(exports, "InferenceErrorCodeAlreadyExistsError", { enumerable: true, get: function () { return package_data_spec_errors_1.InferenceErrorCodeAlreadyExistsError; } });
		Object.defineProperty(exports, "isInferenceErrorCodeAlreadyExistsError", { enumerable: true, get: function () { return package_data_spec_errors_1.isInferenceErrorCodeAlreadyExistsError; } });
		Object.defineProperty(exports, "InferenceErrorCodeRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.InferenceErrorCodeRetiredError; } });
		Object.defineProperty(exports, "isInferenceErrorCodeRetiredError", { enumerable: true, get: function () { return package_data_spec_errors_1.isInferenceErrorCodeRetiredError; } });
		var package_data_adjudication_errors_1 = requirePackageDataAdjudication_errors();
		Object.defineProperty(exports, "ClauseCitationPropertyMismatchError", { enumerable: true, get: function () { return package_data_adjudication_errors_1.ClauseCitationPropertyMismatchError; } });
		Object.defineProperty(exports, "isClauseCitationPropertyMismatchError", { enumerable: true, get: function () { return package_data_adjudication_errors_1.isClauseCitationPropertyMismatchError; } });
		Object.defineProperty(exports, "ErrorCodeCitationPropertyMismatchError", { enumerable: true, get: function () { return package_data_adjudication_errors_1.ErrorCodeCitationPropertyMismatchError; } });
		Object.defineProperty(exports, "isErrorCodeCitationPropertyMismatchError", { enumerable: true, get: function () { return package_data_adjudication_errors_1.isErrorCodeCitationPropertyMismatchError; } });
		var project_model_errors_1 = requireProjectModel_errors();
		Object.defineProperty(exports, "ModuleInstanceNotFoundError", { enumerable: true, get: function () { return project_model_errors_1.ModuleInstanceNotFoundError; } });
		Object.defineProperty(exports, "isModuleInstanceNotFoundError", { enumerable: true, get: function () { return project_model_errors_1.isModuleInstanceNotFoundError; } });
		var validation_error_1 = requireValidation_error();
		Object.defineProperty(exports, "ValidationError", { enumerable: true, get: function () { return validation_error_1.ValidationError; } });
		Object.defineProperty(exports, "isValidationError", { enumerable: true, get: function () { return validation_error_1.isValidationError; } });
		
	} (errors));
	return errors;
}

var netDriveResultDriveMode = {};

var hasRequiredNetDriveResultDriveMode;

function requireNetDriveResultDriveMode () {
	if (hasRequiredNetDriveResultDriveMode) return netDriveResultDriveMode;
	hasRequiredNetDriveResultDriveMode = 1;
	Object.defineProperty(netDriveResultDriveMode, "__esModule", { value: true });
	netDriveResultDriveMode.NET_DRIVE_RESULT_DRIVE_MODES = netDriveResultDriveMode.NetDriveResultDriveMode = void 0;
	netDriveResultDriveMode.isNetDriveResultDriveMode = isNetDriveResultDriveMode;
	var NetDriveResultDriveMode;
	(function (NetDriveResultDriveMode) {
	    NetDriveResultDriveMode["ACTIVE"] = "active";
	    NetDriveResultDriveMode["INACTIVE"] = "inactive";
	    NetDriveResultDriveMode["UNRESOLVED"] = "unresolved";
	})(NetDriveResultDriveMode || (netDriveResultDriveMode.NetDriveResultDriveMode = NetDriveResultDriveMode = {}));
	function isNetDriveResultDriveMode(value) {
	    return Object.values(NetDriveResultDriveMode).includes(value);
	}
	netDriveResultDriveMode.NET_DRIVE_RESULT_DRIVE_MODES = Object.values(NetDriveResultDriveMode);
	
	return netDriveResultDriveMode;
}

var packageStatus = {};

var hasRequiredPackageStatus;

function requirePackageStatus () {
	if (hasRequiredPackageStatus) return packageStatus;
	hasRequiredPackageStatus = 1;
	Object.defineProperty(packageStatus, "__esModule", { value: true });
	packageStatus.packageStatusSchema = packageStatus.PackageStatus = void 0;
	const z = /*@__PURE__*/ requireZod();
	var PackageStatus;
	(function (PackageStatus) {
	    PackageStatus["Draft"] = "draft";
	    PackageStatus["Published"] = "published";
	})(PackageStatus || (packageStatus.PackageStatus = PackageStatus = {}));
	packageStatus.packageStatusSchema = z.nativeEnum(PackageStatus);
	
	return packageStatus;
}

var pinoutValidationResult = {};

var hasRequiredPinoutValidationResult;

function requirePinoutValidationResult () {
	if (hasRequiredPinoutValidationResult) return pinoutValidationResult;
	hasRequiredPinoutValidationResult = 1;
	Object.defineProperty(pinoutValidationResult, "__esModule", { value: true });
	pinoutValidationResult.pinoutValidationResultSchema = void 0;
	const z = /*@__PURE__*/ requireZod();
	const sigma_engine_shared_1 = requireDist$1();
	pinoutValidationResult.pinoutValidationResultSchema = z.object({
	    referenceDesignator: z.string(),
	    packageName: z.string(),
	    customerSignal: z.string(),
	    customerPin: z.string(),
	    packageStoreSignal: z.string(),
	    packageStorePin: z.string(),
	    status: z.nativeEnum(sigma_engine_shared_1.PinoutValidationResultEntryStatus),
	    packageId: z.string(),
	});
	
	return pinoutValidationResult;
}

var buildStatusTransitions = {};

var buildAutomation = {};

var hasRequiredBuildAutomation;

function requireBuildAutomation () {
	if (hasRequiredBuildAutomation) return buildAutomation;
	hasRequiredBuildAutomation = 1;
	Object.defineProperty(buildAutomation, "__esModule", { value: true });
	buildAutomation.BuildStatusActor = buildAutomation.AI_ANALYSIS_RECOVERY_FINDINGS_RUN_ID_PREFIX = buildAutomation.FAST_MODE_ANALYSIS_RUN_ID_PREFIX = buildAutomation.AI_ANALYSIS_INCOMPLETE_REASON_PREFIX = buildAutomation.ANALYSIS_FAILED_STATUS_MESSAGE = buildAutomation.ANALYSIS_OFF_RAMP_REASON_PREFIX = buildAutomation.AnalysisDispatchClaimOutcome = buildAutomation.BuildEnrichmentArm = buildAutomation.BuildAutomationStep = buildAutomation.buildAutomationModeSchema = buildAutomation.BuildAutomationMode = void 0;
	buildAutomation.isBuildAutomationMode = isBuildAutomationMode;
	buildAutomation.isAutomationStepEnabled = isAutomationStepEnabled;
	buildAutomation.isBuildStatusActor = isBuildStatusActor;
	const z = /*@__PURE__*/ requireZod();
	var BuildAutomationMode;
	(function (BuildAutomationMode) {
	    BuildAutomationMode["Manual"] = "manual";
	    BuildAutomationMode["Automatic"] = "automatic";
	    BuildAutomationMode["Demoted"] = "demoted";
	})(BuildAutomationMode || (buildAutomation.BuildAutomationMode = BuildAutomationMode = {}));
	buildAutomation.buildAutomationModeSchema = z.nativeEnum(BuildAutomationMode);
	function isBuildAutomationMode(value) {
	    return Object.values(BuildAutomationMode).includes(value);
	}
	var BuildAutomationStep;
	(function (BuildAutomationStep) {
	    BuildAutomationStep["DispatchAnalysis"] = "dispatch_analysis";
	    BuildAutomationStep["CompleteOnAnalysisSuccess"] = "complete_on_analysis_success";
	    BuildAutomationStep["OffRampOnAnalysisFailure"] = "off_ramp_on_analysis_failure";
	})(BuildAutomationStep || (buildAutomation.BuildAutomationStep = BuildAutomationStep = {}));
	const AUTOMATED_STEPS_BY_MODE = {
	    [BuildAutomationMode.Manual]: [],
	    [BuildAutomationMode.Demoted]: [],
	    [BuildAutomationMode.Automatic]: [
	        BuildAutomationStep.DispatchAnalysis,
	        BuildAutomationStep.CompleteOnAnalysisSuccess,
	        BuildAutomationStep.OffRampOnAnalysisFailure,
	    ],
	};
	function isAutomationStepEnabled(mode, step) {
	    if (mode === undefined) {
	        return false;
	    }
	    return AUTOMATED_STEPS_BY_MODE[mode].includes(step);
	}
	var BuildEnrichmentArm;
	(function (BuildEnrichmentArm) {
	    BuildEnrichmentArm["Ingestion"] = "ingestion";
	    BuildEnrichmentArm["Merge"] = "merge";
	})(BuildEnrichmentArm || (buildAutomation.BuildEnrichmentArm = BuildEnrichmentArm = {}));
	var AnalysisDispatchClaimOutcome;
	(function (AnalysisDispatchClaimOutcome) {
	    AnalysisDispatchClaimOutcome["Claimed"] = "claimed";
	    AnalysisDispatchClaimOutcome["AlreadyClaimed"] = "already_claimed";
	    AnalysisDispatchClaimOutcome["NotEligible"] = "not_eligible";
	})(AnalysisDispatchClaimOutcome || (buildAutomation.AnalysisDispatchClaimOutcome = AnalysisDispatchClaimOutcome = {}));
	buildAutomation.ANALYSIS_OFF_RAMP_REASON_PREFIX = 'Automated analysis failed: ';
	buildAutomation.ANALYSIS_FAILED_STATUS_MESSAGE = 'Analysis failed. Our team has been notified.';
	buildAutomation.AI_ANALYSIS_INCOMPLETE_REASON_PREFIX = 'AI analysis incomplete';
	buildAutomation.FAST_MODE_ANALYSIS_RUN_ID_PREFIX = 'fast-mode-';
	buildAutomation.AI_ANALYSIS_RECOVERY_FINDINGS_RUN_ID_PREFIX = 'recovery-findings-';
	var BuildStatusActor;
	(function (BuildStatusActor) {
	    BuildStatusActor["System"] = "system";
	    BuildStatusActor["FastMode"] = "system:fast-mode";
	    BuildStatusActor["CliResolveBomItems"] = "cli:resolve-bom-items";
	    BuildStatusActor["CliAutomapSignals"] = "cli:automap-signals";
	    BuildStatusActor["CliAutoreviewResolutions"] = "cli:autoreview-resolutions";
	    BuildStatusActor["CliEnrolFastMode"] = "cli:enrol-fast-mode";
	})(BuildStatusActor || (buildAutomation.BuildStatusActor = BuildStatusActor = {}));
	function isBuildStatusActor(actor) {
	    return Object.values(BuildStatusActor).includes(actor);
	}
	
	return buildAutomation;
}

var hasRequiredBuildStatusTransitions;

function requireBuildStatusTransitions () {
	if (hasRequiredBuildStatusTransitions) return buildStatusTransitions;
	hasRequiredBuildStatusTransitions = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.APPROACHING_ANALYSIS_BUILD_STATUSES = exports.ANALYZABLE_BUILD_STATUSES = exports.POST_REVIEW_ERROR_ORIGIN_STATUSES = exports.PRE_REVIEW_BUILD_STATUSES = exports.TIMED_OUT_PREDECESSORS_BY_REASON = exports.BUILD_STATUS_TRANSITIONS = exports.TransitionOutcome = void 0;
		exports.isPreReviewBuildStatus = isPreReviewBuildStatus;
		exports.canBeginBomMerge = canBeginBomMerge;
		exports.canTransition = canTransition;
		exports.legalPredecessorsOf = legalPredecessorsOf;
		exports.isTerminalBuildStatus = isTerminalBuildStatus;
		exports.isAnalyzableBuildStatus = isAnalyzableBuildStatus;
		exports.isBoundForAnalysis = isBoundForAnalysis;
		const build_automation_1 = requireBuildAutomation();
		const build_status_1 = requireBuildStatus();
		var TransitionOutcome;
		(function (TransitionOutcome) {
		    TransitionOutcome["Transitioned"] = "transitioned";
		    TransitionOutcome["AlreadyInTarget"] = "already_in_target";
		    TransitionOutcome["Refused"] = "refused";
		})(TransitionOutcome || (exports.TransitionOutcome = TransitionOutcome = {}));
		exports.BUILD_STATUS_TRANSITIONS = {
		    [build_status_1.BuildStatus.Pending]: [build_status_1.BuildStatus.Validating, build_status_1.BuildStatus.Error, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.Validating]: [build_status_1.BuildStatus.Processing, build_status_1.BuildStatus.Error, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.Processing]: [
		        build_status_1.BuildStatus.BomAwaitingReview,
		        build_status_1.BuildStatus.Error,
		        build_status_1.BuildStatus.TimedOut,
		        build_status_1.BuildStatus.Abandoned,
		    ],
		    [build_status_1.BuildStatus.Error]: [
		        build_status_1.BuildStatus.Processing,
		        build_status_1.BuildStatus.BomAwaitingReview,
		        build_status_1.BuildStatus.ProcessingBom,
		        build_status_1.BuildStatus.ReadyToAnalyze,
		        build_status_1.BuildStatus.Abandoned,
		    ],
		    [build_status_1.BuildStatus.BomAwaitingReview]: [build_status_1.BuildStatus.ProcessingBom, build_status_1.BuildStatus.TimedOut, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.ProcessingBom]: [
		        build_status_1.BuildStatus.ReadyToAnalyze,
		        build_status_1.BuildStatus.Error,
		        build_status_1.BuildStatus.TimedOut,
		        build_status_1.BuildStatus.Abandoned,
		    ],
		    [build_status_1.BuildStatus.ReadyToAnalyze]: [
		        build_status_1.BuildStatus.ProcessingDesign,
		        build_status_1.BuildStatus.TimedOut,
		        build_status_1.BuildStatus.CreditRequired,
		        build_status_1.BuildStatus.Abandoned,
		    ],
		    [build_status_1.BuildStatus.ProcessingDesign]: [
		        build_status_1.BuildStatus.AnalysisComplete,
		        build_status_1.BuildStatus.Complete,
		        build_status_1.BuildStatus.Error,
		        build_status_1.BuildStatus.Abandoned,
		    ],
		    [build_status_1.BuildStatus.AnalysisComplete]: [build_status_1.BuildStatus.Complete, build_status_1.BuildStatus.ProcessingDesign, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.Complete]: [build_status_1.BuildStatus.ProcessingDesign, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.TimedOut]: [build_status_1.BuildStatus.Processing, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.CreditRequired]: [build_status_1.BuildStatus.ProcessingDesign, build_status_1.BuildStatus.Abandoned],
		    [build_status_1.BuildStatus.Abandoned]: [],
		    [build_status_1.BuildStatus.Unknown]: [],
		};
		exports.TIMED_OUT_PREDECESSORS_BY_REASON = {
		    [build_status_1.BuildTimeoutReason.ParseWait]: [build_status_1.BuildStatus.Processing],
		    [build_status_1.BuildTimeoutReason.NetlistWait]: [
		        build_status_1.BuildStatus.Processing,
		        build_status_1.BuildStatus.BomAwaitingReview,
		        build_status_1.BuildStatus.ProcessingBom,
		        build_status_1.BuildStatus.ReadyToAnalyze,
		    ],
		};
		exports.PRE_REVIEW_BUILD_STATUSES = [
		    build_status_1.BuildStatus.Pending,
		    build_status_1.BuildStatus.Validating,
		    build_status_1.BuildStatus.Processing,
		];
		function isPreReviewBuildStatus(status) {
		    return exports.PRE_REVIEW_BUILD_STATUSES.includes(status);
		}
		exports.POST_REVIEW_ERROR_ORIGIN_STATUSES = Object.values(build_status_1.BuildStatus).filter((status) => !isPreReviewBuildStatus(status) && status !== build_status_1.BuildStatus.Error && status !== build_status_1.BuildStatus.Unknown);
		function canBeginBomMerge(status, erroredFromStatus) {
		    if (!canTransition(status, build_status_1.BuildStatus.ProcessingBom)) {
		        return false;
		    }
		    return (status !== build_status_1.BuildStatus.Error ||
		        (erroredFromStatus !== undefined && exports.POST_REVIEW_ERROR_ORIGIN_STATUSES.includes(erroredFromStatus)));
		}
		function canTransition(from, to) {
		    return exports.BUILD_STATUS_TRANSITIONS[from].includes(to);
		}
		function legalPredecessorsOf(target) {
		    return Object.keys(exports.BUILD_STATUS_TRANSITIONS).filter((from) => canTransition(from, target));
		}
		function isTerminalBuildStatus(status) {
		    return exports.BUILD_STATUS_TRANSITIONS[status].length === 0;
		}
		exports.ANALYZABLE_BUILD_STATUSES = new Set(legalPredecessorsOf(build_status_1.BuildStatus.ProcessingDesign));
		function isAnalyzableBuildStatus(status) {
		    return exports.ANALYZABLE_BUILD_STATUSES.has(status);
		}
		exports.APPROACHING_ANALYSIS_BUILD_STATUSES = new Set([
		    build_status_1.BuildStatus.Validating,
		    build_status_1.BuildStatus.Processing,
		    build_status_1.BuildStatus.BomAwaitingReview,
		    build_status_1.BuildStatus.ProcessingBom,
		]);
		function isBoundForAnalysis(status, automationMode) {
		    if (exports.APPROACHING_ANALYSIS_BUILD_STATUSES.has(status)) {
		        return true;
		    }
		    return (status === build_status_1.BuildStatus.ReadyToAnalyze &&
		        (0, build_automation_1.isAutomationStepEnabled)(automationMode, build_automation_1.BuildAutomationStep.DispatchAnalysis));
		}
		
	} (buildStatusTransitions));
	return buildStatusTransitions;
}

var pipelineOps = {};

var hasRequiredPipelineOps;

function requirePipelineOps () {
	if (hasRequiredPipelineOps) return pipelineOps;
	hasRequiredPipelineOps = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.PIPELINE_OPS_UNLISTED_RUN_LIMIT = exports.PipelineOpsUnlistedReason = exports.AiAnalysisRecoveryBlockerKind = exports.AiAnalysisRecoveryMode = exports.PipelineOpsAction = exports.PipelineNetlistFailure = exports.PIPELINE_WORKFLOW_CLOSED_STATUSES = exports.PipelineWorkflowStatus = exports.PIPELINE_OPS_INTERNAL_BILLING_CLASS = exports.PIPELINE_OPS_EXCLUSIONS = exports.PipelineOpsExclusion = exports.PIPELINE_OPS_MAX_WINDOW_DAYS = exports.PIPELINE_OPS_DEFAULT_WINDOW_DAYS = exports.PIPELINE_AWAITING_STATE_BY_ATTRIBUTE = exports.PipelineAwaitingState = exports.NETLIST_WAIT_EXPIRED_WORKFLOW_STAGE = exports.PipelineStageState = exports.DeliveryHealthReason = exports.PIPELINE_STAGE_BY_BUILD_STATUS = exports.PIPELINE_STAGES = exports.PipelineStage = void 0;
		exports.pipelineStageRank = pipelineStageRank;
		exports.pipelineAwaitingStateFromAttribute = pipelineAwaitingStateFromAttribute;
		exports.isPipelineOpsExclusion = isPipelineOpsExclusion;
		exports.pipelineWorkflowStatusFromTemporal = pipelineWorkflowStatusFromTemporal;
		const build_status_1 = requireBuildStatus();
		var PipelineStage;
		(function (PipelineStage) {
		    PipelineStage["Upload"] = "upload";
		    PipelineStage["Validate"] = "validate";
		    PipelineStage["Resolve"] = "resolve";
		    PipelineStage["BomReview"] = "bom_review";
		    PipelineStage["Merge"] = "merge";
		    PipelineStage["Netlist"] = "netlist";
		    PipelineStage["AnalysisGate"] = "analysis_gate";
		    PipelineStage["Analysis"] = "analysis";
		    PipelineStage["Review"] = "review";
		    PipelineStage["Deliver"] = "deliver";
		})(PipelineStage || (exports.PipelineStage = PipelineStage = {}));
		exports.PIPELINE_STAGES = [
		    PipelineStage.Upload,
		    PipelineStage.Validate,
		    PipelineStage.Resolve,
		    PipelineStage.BomReview,
		    PipelineStage.Merge,
		    PipelineStage.Netlist,
		    PipelineStage.AnalysisGate,
		    PipelineStage.Analysis,
		    PipelineStage.Review,
		    PipelineStage.Deliver,
		];
		exports.PIPELINE_STAGE_BY_BUILD_STATUS = {
		    [build_status_1.BuildStatus.Pending]: PipelineStage.Upload,
		    [build_status_1.BuildStatus.Validating]: PipelineStage.Validate,
		    [build_status_1.BuildStatus.Processing]: PipelineStage.Resolve,
		    [build_status_1.BuildStatus.BomAwaitingReview]: PipelineStage.BomReview,
		    [build_status_1.BuildStatus.ProcessingBom]: PipelineStage.Merge,
		    [build_status_1.BuildStatus.ReadyToAnalyze]: PipelineStage.AnalysisGate,
		    [build_status_1.BuildStatus.ProcessingDesign]: PipelineStage.Analysis,
		    [build_status_1.BuildStatus.AnalysisComplete]: PipelineStage.Review,
		    [build_status_1.BuildStatus.Complete]: PipelineStage.Deliver,
		};
		var DeliveryHealthReason;
		(function (DeliveryHealthReason) {
		    DeliveryHealthReason["Breached"] = "breached";
		    DeliveryHealthReason["AtRisk"] = "at_risk";
		    DeliveryHealthReason["Stalled"] = "stalled";
		    DeliveryHealthReason["Quiet"] = "quiet";
		    DeliveryHealthReason["Good"] = "good";
		    DeliveryHealthReason["StageOverdue"] = "stage_overdue";
		    DeliveryHealthReason["Blocked"] = "blocked";
		    DeliveryHealthReason["Abandoned"] = "abandoned";
		    DeliveryHealthReason["TimedOut"] = "timed_out";
		    DeliveryHealthReason["CreditRequired"] = "credit_required";
		})(DeliveryHealthReason || (exports.DeliveryHealthReason = DeliveryHealthReason = {}));
		var PipelineStageState;
		(function (PipelineStageState) {
		    PipelineStageState["Ahead"] = "ahead";
		    PipelineStageState["Done"] = "done";
		    PipelineStageState["Active"] = "active";
		    PipelineStageState["Overdue"] = "overdue";
		    PipelineStageState["Blocked"] = "blocked";
		    PipelineStageState["Skipped"] = "skipped";
		})(PipelineStageState || (exports.PipelineStageState = PipelineStageState = {}));
		exports.NETLIST_WAIT_EXPIRED_WORKFLOW_STAGE = 'netlist-timed-out';
		function pipelineStageRank(stage) {
		    return exports.PIPELINE_STAGES.indexOf(stage);
		}
		var PipelineAwaitingState;
		(function (PipelineAwaitingState) {
		    PipelineAwaitingState["None"] = "none";
		    PipelineAwaitingState["ParseErrorResolution"] = "parse_error_resolution";
		    PipelineAwaitingState["NetlistUpload"] = "netlist_upload";
		    PipelineAwaitingState["NetlistErrorReview"] = "netlist_error_review";
		    PipelineAwaitingState["BomReview"] = "bom_review";
		    PipelineAwaitingState["AnalysisStart"] = "analysis_start";
		    PipelineAwaitingState["NetlistClearance"] = "netlist_clearance";
		    PipelineAwaitingState["Delivery"] = "delivery";
		    PipelineAwaitingState["Other"] = "other";
		})(PipelineAwaitingState || (exports.PipelineAwaitingState = PipelineAwaitingState = {}));
		exports.PIPELINE_AWAITING_STATE_BY_ATTRIBUTE = {
		    parseErrorResolution: PipelineAwaitingState.ParseErrorResolution,
		    netlistUpload: PipelineAwaitingState.NetlistUpload,
		    netlistErrorReview: PipelineAwaitingState.NetlistErrorReview,
		};
		function pipelineAwaitingStateFromAttribute(attribute) {
		    if (attribute === undefined || attribute === '') {
		        return PipelineAwaitingState.None;
		    }
		    return exports.PIPELINE_AWAITING_STATE_BY_ATTRIBUTE[attribute] ?? PipelineAwaitingState.Other;
		}
		exports.PIPELINE_OPS_DEFAULT_WINDOW_DAYS = 7;
		exports.PIPELINE_OPS_MAX_WINDOW_DAYS = 90;
		var PipelineOpsExclusion;
		(function (PipelineOpsExclusion) {
		    PipelineOpsExclusion["ArchivedProject"] = "archived_project";
		    PipelineOpsExclusion["DemoProject"] = "demo_project";
		    PipelineOpsExclusion["InternalOrg"] = "internal_org";
		    PipelineOpsExclusion["Superseded"] = "superseded";
		    PipelineOpsExclusion["Dormant"] = "dormant";
		    PipelineOpsExclusion["Delivered"] = "delivered";
		})(PipelineOpsExclusion || (exports.PipelineOpsExclusion = PipelineOpsExclusion = {}));
		exports.PIPELINE_OPS_EXCLUSIONS = [
		    PipelineOpsExclusion.ArchivedProject,
		    PipelineOpsExclusion.DemoProject,
		    PipelineOpsExclusion.InternalOrg,
		    PipelineOpsExclusion.Superseded,
		    PipelineOpsExclusion.Dormant,
		    PipelineOpsExclusion.Delivered,
		];
		function isPipelineOpsExclusion(value) {
		    return Object.values(PipelineOpsExclusion).includes(value);
		}
		exports.PIPELINE_OPS_INTERNAL_BILLING_CLASS = 'internal';
		var PipelineWorkflowStatus;
		(function (PipelineWorkflowStatus) {
		    PipelineWorkflowStatus["Running"] = "running";
		    PipelineWorkflowStatus["Completed"] = "completed";
		    PipelineWorkflowStatus["Failed"] = "failed";
		    PipelineWorkflowStatus["Canceled"] = "canceled";
		    PipelineWorkflowStatus["Terminated"] = "terminated";
		    PipelineWorkflowStatus["TimedOut"] = "timed_out";
		    PipelineWorkflowStatus["ContinuedAsNew"] = "continued_as_new";
		})(PipelineWorkflowStatus || (exports.PipelineWorkflowStatus = PipelineWorkflowStatus = {}));
		const PIPELINE_WORKFLOW_STATUS_BY_TEMPORAL_NAME = {
		    RUNNING: PipelineWorkflowStatus.Running,
		    COMPLETED: PipelineWorkflowStatus.Completed,
		    FAILED: PipelineWorkflowStatus.Failed,
		    CANCELED: PipelineWorkflowStatus.Canceled,
		    CANCELLED: PipelineWorkflowStatus.Canceled,
		    TERMINATED: PipelineWorkflowStatus.Terminated,
		    TIMED_OUT: PipelineWorkflowStatus.TimedOut,
		    CONTINUED_AS_NEW: PipelineWorkflowStatus.ContinuedAsNew,
		};
		function pipelineWorkflowStatusFromTemporal(name) {
		    if (name === undefined) {
		        return undefined;
		    }
		    return PIPELINE_WORKFLOW_STATUS_BY_TEMPORAL_NAME[name.toUpperCase()];
		}
		exports.PIPELINE_WORKFLOW_CLOSED_STATUSES = [
		    PipelineWorkflowStatus.Completed,
		    PipelineWorkflowStatus.Failed,
		    PipelineWorkflowStatus.Canceled,
		    PipelineWorkflowStatus.Terminated,
		    PipelineWorkflowStatus.TimedOut,
		    PipelineWorkflowStatus.ContinuedAsNew,
		];
		var PipelineNetlistFailure;
		(function (PipelineNetlistFailure) {
		    PipelineNetlistFailure["Generation"] = "generation";
		    PipelineNetlistFailure["Validation"] = "validation";
		})(PipelineNetlistFailure || (exports.PipelineNetlistFailure = PipelineNetlistFailure = {}));
		var PipelineOpsAction;
		(function (PipelineOpsAction) {
		    PipelineOpsAction["NetlistUploaded"] = "netlist_uploaded";
		    PipelineOpsAction["NetlistErrorsAccepted"] = "netlist_errors_accepted";
		    PipelineOpsAction["AiAnalysisRecoveryStarted"] = "ai_analysis_recovery_started";
		})(PipelineOpsAction || (exports.PipelineOpsAction = PipelineOpsAction = {}));
		var AiAnalysisRecoveryMode;
		(function (AiAnalysisRecoveryMode) {
		    AiAnalysisRecoveryMode["FastMode"] = "fast_mode";
		    AiAnalysisRecoveryMode["RetryOnly"] = "retry_only";
		})(AiAnalysisRecoveryMode || (exports.AiAnalysisRecoveryMode = AiAnalysisRecoveryMode = {}));
		var AiAnalysisRecoveryBlockerKind;
		(function (AiAnalysisRecoveryBlockerKind) {
		    AiAnalysisRecoveryBlockerKind["LiveLease"] = "live_lease";
		    AiAnalysisRecoveryBlockerKind["AnalysisRunning"] = "analysis_running";
		    AiAnalysisRecoveryBlockerKind["RunsUnknown"] = "runs_unknown";
		    AiAnalysisRecoveryBlockerKind["OnDemandTrigger"] = "on_demand_trigger";
		})(AiAnalysisRecoveryBlockerKind || (exports.AiAnalysisRecoveryBlockerKind = AiAnalysisRecoveryBlockerKind = {}));
		var PipelineOpsUnlistedReason;
		(function (PipelineOpsUnlistedReason) {
		    PipelineOpsUnlistedReason["BuildNotShown"] = "build_not_shown";
		    PipelineOpsUnlistedReason["BuildMissing"] = "build_missing";
		})(PipelineOpsUnlistedReason || (exports.PipelineOpsUnlistedReason = PipelineOpsUnlistedReason = {}));
		exports.PIPELINE_OPS_UNLISTED_RUN_LIMIT = 50;
		
	} (pipelineOps));
	return pipelineOps;
}

var deliveryHealth = {};

var hasRequiredDeliveryHealth;

function requireDeliveryHealth () {
	if (hasRequiredDeliveryHealth) return deliveryHealth;
	hasRequiredDeliveryHealth = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DELIVERY_HEALTH_SEVERITY = exports.DeliveryFunnelGate = exports.DeliverySlaSource = exports.DeliveryDatasheetCoverageJudgment = exports.DeliverySlaJudgment = void 0;
		exports.deliveryHealthSeverity = deliveryHealthSeverity;
		exports.isDeliverySlaJudgment = isDeliverySlaJudgment;
		exports.isDeliveryFunnelGate = isDeliveryFunnelGate;
		exports.isDeliveryHealthReason = isDeliveryHealthReason;
		exports.isDeliverySlaSource = isDeliverySlaSource;
		exports.isDeliveryDatasheetCoverageJudgment = isDeliveryDatasheetCoverageJudgment;
		const pipeline_ops_1 = requirePipelineOps();
		var DeliverySlaJudgment;
		(function (DeliverySlaJudgment) {
		    DeliverySlaJudgment["NotStarted"] = "not_started";
		    DeliverySlaJudgment["OnTrack"] = "on_track";
		    DeliverySlaJudgment["AtRisk"] = "at_risk";
		    DeliverySlaJudgment["Breached"] = "breached";
		})(DeliverySlaJudgment || (exports.DeliverySlaJudgment = DeliverySlaJudgment = {}));
		var DeliveryDatasheetCoverageJudgment;
		(function (DeliveryDatasheetCoverageJudgment) {
		    DeliveryDatasheetCoverageJudgment["NotCounted"] = "not_counted";
		    DeliveryDatasheetCoverageJudgment["Met"] = "met";
		    DeliveryDatasheetCoverageJudgment["BelowTarget"] = "below_target";
		})(DeliveryDatasheetCoverageJudgment || (exports.DeliveryDatasheetCoverageJudgment = DeliveryDatasheetCoverageJudgment = {}));
		var DeliverySlaSource;
		(function (DeliverySlaSource) {
		    DeliverySlaSource["OrgOverride"] = "org_override";
		    DeliverySlaSource["Default"] = "default";
		})(DeliverySlaSource || (exports.DeliverySlaSource = DeliverySlaSource = {}));
		var DeliveryFunnelGate;
		(function (DeliveryFunnelGate) {
		    DeliveryFunnelGate["Green"] = "green";
		    DeliveryFunnelGate["Yellow"] = "yellow";
		    DeliveryFunnelGate["Blue"] = "blue";
		    DeliveryFunnelGate["Empty"] = "empty";
		})(DeliveryFunnelGate || (exports.DeliveryFunnelGate = DeliveryFunnelGate = {}));
		exports.DELIVERY_HEALTH_SEVERITY = [
		    pipeline_ops_1.DeliveryHealthReason.Blocked,
		    pipeline_ops_1.DeliveryHealthReason.Breached,
		    pipeline_ops_1.DeliveryHealthReason.AtRisk,
		    pipeline_ops_1.DeliveryHealthReason.Stalled,
		    pipeline_ops_1.DeliveryHealthReason.TimedOut,
		    pipeline_ops_1.DeliveryHealthReason.CreditRequired,
		    pipeline_ops_1.DeliveryHealthReason.Quiet,
		    pipeline_ops_1.DeliveryHealthReason.StageOverdue,
		    pipeline_ops_1.DeliveryHealthReason.Good,
		    pipeline_ops_1.DeliveryHealthReason.Abandoned,
		];
		function deliveryHealthSeverity(reason) {
		    return exports.DELIVERY_HEALTH_SEVERITY.indexOf(reason);
		}
		function isDeliverySlaJudgment(value) {
		    return Object.values(DeliverySlaJudgment).includes(value);
		}
		function isDeliveryFunnelGate(value) {
		    return Object.values(DeliveryFunnelGate).includes(value);
		}
		function isDeliveryHealthReason(value) {
		    return Object.values(pipeline_ops_1.DeliveryHealthReason).includes(value);
		}
		function isDeliverySlaSource(value) {
		    return Object.values(DeliverySlaSource).includes(value);
		}
		function isDeliveryDatasheetCoverageJudgment(value) {
		    return Object.values(DeliveryDatasheetCoverageJudgment).includes(value);
		}
		
	} (deliveryHealth));
	return deliveryHealth;
}

var domainEvents = {};

var hasRequiredDomainEvents;

function requireDomainEvents () {
	if (hasRequiredDomainEvents) return domainEvents;
	hasRequiredDomainEvents = 1;
	Object.defineProperty(domainEvents, "__esModule", { value: true });
	domainEvents.ReviewerRoleChangePayloadSchema = domainEvents.ReviewerRoleDomainEventType = domainEvents.PipelinePhase = domainEvents.BuildDomainEventType = domainEvents.DomainEventSubjectType = void 0;
	domainEvents.isPipelinePhase = isPipelinePhase;
	const z = /*@__PURE__*/ requireZod();
	const reviewer_role_1 = requireReviewerRole();
	var DomainEventSubjectType;
	(function (DomainEventSubjectType) {
	    DomainEventSubjectType["Build"] = "build";
	    DomainEventSubjectType["User"] = "user";
	})(DomainEventSubjectType || (domainEvents.DomainEventSubjectType = DomainEventSubjectType = {}));
	var BuildDomainEventType;
	(function (BuildDomainEventType) {
	    BuildDomainEventType["StatusTransitioned"] = "build.status_transitioned";
	    BuildDomainEventType["StatusForced"] = "build.status_forced";
	    BuildDomainEventType["AutomationModeChanged"] = "build.automation_mode_changed";
	    BuildDomainEventType["IngestionEnrichmentCompleted"] = "build.ingestion_enrichment_completed";
	    BuildDomainEventType["MergeEnrichmentCompleted"] = "build.merge_enrichment_completed";
	    BuildDomainEventType["ProcessingRestarted"] = "build.processing_restarted";
	    BuildDomainEventType["AnalysisDispatchClaimed"] = "build.analysis_dispatch_claimed";
	    BuildDomainEventType["AnalysisDispatchReleased"] = "build.analysis_dispatch_released";
	    BuildDomainEventType["StatusMessageSet"] = "build.status_message_set";
	    BuildDomainEventType["AnalysisSucceededAwaitingCompletion"] = "build.analysis_succeeded_awaiting_completion";
	    BuildDomainEventType["AnalysisStarted"] = "build.analysis_started";
	    BuildDomainEventType["AnalysisFinished"] = "build.analysis_finished";
	    BuildDomainEventType["PipelinePhaseCompleted"] = "build.pipeline_phase_completed";
	    BuildDomainEventType["OpsActionTaken"] = "build.ops_action_taken";
	    BuildDomainEventType["NetlistGateArmed"] = "build.netlist_gate_armed";
	    BuildDomainEventType["NetlistCleared"] = "build.netlist_cleared";
	})(BuildDomainEventType || (domainEvents.BuildDomainEventType = BuildDomainEventType = {}));
	var PipelinePhase;
	(function (PipelinePhase) {
	    PipelinePhase["ModuleResolution"] = "module_resolution";
	    PipelinePhase["PackageUnderstanding"] = "package_understanding";
	    PipelinePhase["AutomapSignals"] = "automap_signals";
	    PipelinePhase["AutoreviewResolutions"] = "autoreview_resolutions";
	})(PipelinePhase || (domainEvents.PipelinePhase = PipelinePhase = {}));
	function isPipelinePhase(value) {
	    return Object.values(PipelinePhase).includes(value);
	}
	var ReviewerRoleDomainEventType;
	(function (ReviewerRoleDomainEventType) {
	    ReviewerRoleDomainEventType["Granted"] = "user.reviewer_role_granted";
	    ReviewerRoleDomainEventType["Revoked"] = "user.reviewer_role_revoked";
	})(ReviewerRoleDomainEventType || (domainEvents.ReviewerRoleDomainEventType = ReviewerRoleDomainEventType = {}));
	domainEvents.ReviewerRoleChangePayloadSchema = z.object({
	    previousRole: reviewer_role_1.ReviewerRoleSchema.nullable(),
	    role: reviewer_role_1.ReviewerRoleSchema.nullable(),
	});
	
	return domainEvents;
}

var restartProcessingOutcome = {};

var hasRequiredRestartProcessingOutcome;

function requireRestartProcessingOutcome () {
	if (hasRequiredRestartProcessingOutcome) return restartProcessingOutcome;
	hasRequiredRestartProcessingOutcome = 1;
	Object.defineProperty(restartProcessingOutcome, "__esModule", { value: true });
	restartProcessingOutcome.restartProcessingOutcomeSchema = restartProcessingOutcome.RestartProcessingOutcome = void 0;
	const z = /*@__PURE__*/ requireZod();
	var RestartProcessingOutcome;
	(function (RestartProcessingOutcome) {
	    RestartProcessingOutcome["Started"] = "started";
	    RestartProcessingOutcome["AlreadyRunning"] = "already_running";
	})(RestartProcessingOutcome || (restartProcessingOutcome.RestartProcessingOutcome = RestartProcessingOutcome = {}));
	restartProcessingOutcome.restartProcessingOutcomeSchema = z.nativeEnum(RestartProcessingOutcome);
	
	return restartProcessingOutcome;
}

var workflowStartOutcome = {};

var hasRequiredWorkflowStartOutcome;

function requireWorkflowStartOutcome () {
	if (hasRequiredWorkflowStartOutcome) return workflowStartOutcome;
	hasRequiredWorkflowStartOutcome = 1;
	Object.defineProperty(workflowStartOutcome, "__esModule", { value: true });
	workflowStartOutcome.WorkflowStartOutcome = void 0;
	var WorkflowStartOutcome;
	(function (WorkflowStartOutcome) {
	    WorkflowStartOutcome["Started"] = "started";
	    WorkflowStartOutcome["AlreadyRunning"] = "already_running";
	    WorkflowStartOutcome["Notified"] = "notified";
	    WorkflowStartOutcome["Skipped"] = "skipped";
	})(WorkflowStartOutcome || (workflowStartOutcome.WorkflowStartOutcome = WorkflowStartOutcome = {}));
	
	return workflowStartOutcome;
}

var analysisGateDispatch = {};

var hasRequiredAnalysisGateDispatch;

function requireAnalysisGateDispatch () {
	if (hasRequiredAnalysisGateDispatch) return analysisGateDispatch;
	hasRequiredAnalysisGateDispatch = 1;
	Object.defineProperty(analysisGateDispatch, "__esModule", { value: true });
	analysisGateDispatch.analysisGateDispatchSchema = analysisGateDispatch.AnalysisGateDispatch = void 0;
	const z = /*@__PURE__*/ requireZod();
	var AnalysisGateDispatch;
	(function (AnalysisGateDispatch) {
	    AnalysisGateDispatch["Started"] = "started";
	    AnalysisGateDispatch["AlreadyRunning"] = "already_running";
	    AnalysisGateDispatch["NotStarted"] = "not_started";
	})(AnalysisGateDispatch || (analysisGateDispatch.AnalysisGateDispatch = AnalysisGateDispatch = {}));
	analysisGateDispatch.analysisGateDispatchSchema = z.nativeEnum(AnalysisGateDispatch);
	
	return analysisGateDispatch;
}

var workflowStartStatus = {};

var hasRequiredWorkflowStartStatus;

function requireWorkflowStartStatus () {
	if (hasRequiredWorkflowStartStatus) return workflowStartStatus;
	hasRequiredWorkflowStartStatus = 1;
	Object.defineProperty(workflowStartStatus, "__esModule", { value: true });
	workflowStartStatus.workflowStartStatusSchema = workflowStartStatus.WorkflowStartStatus = void 0;
	workflowStartStatus.isWorkflowStartStatus = isWorkflowStartStatus;
	const z = /*@__PURE__*/ requireZod();
	var WorkflowStartStatus;
	(function (WorkflowStartStatus) {
	    WorkflowStartStatus["Started"] = "started";
	    WorkflowStartStatus["AlreadyRunning"] = "already_running";
	    WorkflowStartStatus["AlreadyCompleted"] = "already_completed";
	})(WorkflowStartStatus || (workflowStartStatus.WorkflowStartStatus = WorkflowStartStatus = {}));
	workflowStartStatus.workflowStartStatusSchema = z.nativeEnum(WorkflowStartStatus);
	function isWorkflowStartStatus(value) {
	    return Object.values(WorkflowStartStatus).includes(value);
	}
	
	return workflowStartStatus;
}

var catalogueDatasheetAttachOutcome = {};

var hasRequiredCatalogueDatasheetAttachOutcome;

function requireCatalogueDatasheetAttachOutcome () {
	if (hasRequiredCatalogueDatasheetAttachOutcome) return catalogueDatasheetAttachOutcome;
	hasRequiredCatalogueDatasheetAttachOutcome = 1;
	Object.defineProperty(catalogueDatasheetAttachOutcome, "__esModule", { value: true });
	catalogueDatasheetAttachOutcome.MAX_CATALOGUE_PICK_ROWS = catalogueDatasheetAttachOutcome.CatalogueDatasheetAttachOutcome = void 0;
	var CatalogueDatasheetAttachOutcome;
	(function (CatalogueDatasheetAttachOutcome) {
	    CatalogueDatasheetAttachOutcome["Attached"] = "attached";
	    CatalogueDatasheetAttachOutcome["NotFound"] = "not_found";
	    CatalogueDatasheetAttachOutcome["Superseded"] = "superseded";
	    CatalogueDatasheetAttachOutcome["Divided"] = "divided";
	})(CatalogueDatasheetAttachOutcome || (catalogueDatasheetAttachOutcome.CatalogueDatasheetAttachOutcome = CatalogueDatasheetAttachOutcome = {}));
	catalogueDatasheetAttachOutcome.MAX_CATALOGUE_PICK_ROWS = 5000;
	
	return catalogueDatasheetAttachOutcome;
}

var revision = {};

var hasRequiredRevision;

function requireRevision () {
	if (hasRequiredRevision) return revision;
	hasRequiredRevision = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.revisionWithFilesSchema = exports.revisionFileSchema = exports.revisionSchema = exports.revisionStatusSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const build_status_1 = requireBuildStatus();
		const build_automation_1 = requireBuildAutomation();
		exports.revisionStatusSchema = build_status_1.buildStatusSchema;
		exports.revisionSchema = z.object({
		    id: z.string(),
		    status: exports.revisionStatusSchema,
		    statusMessage: z.string().optional(),
		    automationMode: build_automation_1.buildAutomationModeSchema.optional(),
		    displayName: z.string().optional(),
		    hasUploadedFiles: z.boolean(),
		    erroredFromStatus: exports.revisionStatusSchema.optional(),
		    createdAt: z.string(),
		    updatedAt: z.string(),
		});
		exports.revisionFileSchema = z.object({
		    id: z.string(),
		    name: z.string(),
		    uploadedAt: z.string(),
		    size: z.string(),
		    relativePath: z.string().optional(),
		    uploadId: z.string().optional(),
		});
		exports.revisionWithFilesSchema = exports.revisionSchema.extend({
		    files: z.array(exports.revisionFileSchema),
		});
		
	} (revision));
	return revision;
}

var build = {};

var hasRequiredBuild;

function requireBuild () {
	if (hasRequiredBuild) return build;
	hasRequiredBuild = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.buildWithFilesSchema = exports.buildFileSchema = exports.buildSchema = void 0;
		const z = /*@__PURE__*/ requireZod();
		const build_status_1 = requireBuildStatus();
		const build_automation_1 = requireBuildAutomation();
		exports.buildSchema = z.object({
		    id: z.string(),
		    projectId: z.string(),
		    buildNumber: z.number().int().min(0),
		    buildCompleted: z.date().optional(),
		    status: build_status_1.buildStatusSchema,
		    statusMessage: z.string().optional(),
		    erroredFromStatus: build_status_1.buildStatusSchema.optional(),
		    automationMode: build_automation_1.buildAutomationModeSchema.optional(),
		    result: z.string().optional(),
		    type: z.string(),
		    typeRef: z.string().optional(),
		    triggerEvent: z.string().optional(),
		    triggerUrl: z.string().optional(),
		    operationId: z.string().optional(),
		    triggeredByUser: z.string().optional(),
		    data: z.object({}).passthrough().optional(),
		    displayName: z.string().optional(),
		    hasUploadedFiles: z.boolean(),
		    createdAt: z.date().optional(),
		    updatedAt: z.date().optional(),
		});
		exports.buildFileSchema = z.object({
		    id: z.string(),
		    name: z.string(),
		    uploadedAt: z.date(),
		    sizeBytes: z.number().int().min(0),
		    relativePath: z.string().optional(),
		    uploadId: z.string().optional(),
		});
		exports.buildWithFilesSchema = exports.buildSchema.extend({
		    files: z.array(exports.buildFileSchema),
		});
		
	} (build));
	return build;
}

var customerFacingBomItem = {};

var hasRequiredCustomerFacingBomItem;

function requireCustomerFacingBomItem () {
	if (hasRequiredCustomerFacingBomItem) return customerFacingBomItem;
	hasRequiredCustomerFacingBomItem = 1;
	Object.defineProperty(customerFacingBomItem, "__esModule", { value: true });
	customerFacingBomItem.customerFacingBomItemStaticMetadataSchema = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	customerFacingBomItem.customerFacingBomItemStaticMetadataSchema = zod_1.z.object({
	    referenceDesignator: zod_1.z.string(),
	    isCadstromReviewed: zod_1.z.boolean(),
	    value: zod_1.z.string().nullable(),
	    numComponentPins: zod_1.z.number().int().nonnegative(),
	    numComponentSignals: zod_1.z.number().int().nonnegative(),
	    isVirtual: zod_1.z.boolean(),
	    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.null()])),
	});
	
	return customerFacingBomItem;
}

var hilModuleResolutionType = {};

var hasRequiredHilModuleResolutionType;

function requireHilModuleResolutionType () {
	if (hasRequiredHilModuleResolutionType) return hilModuleResolutionType;
	hasRequiredHilModuleResolutionType = 1;
	Object.defineProperty(hilModuleResolutionType, "__esModule", { value: true });
	hilModuleResolutionType.hilModuleResolutionTypeSchema = hilModuleResolutionType.HilModuleResolutionType = void 0;
	const zod_1 = /*@__PURE__*/ requireZod();
	var HilModuleResolutionType;
	(function (HilModuleResolutionType) {
	    HilModuleResolutionType["SigmaModel"] = "SIGMA_MODEL";
	    HilModuleResolutionType["PackageStore"] = "PACKAGE_STORE";
	    HilModuleResolutionType["NonElectrical"] = "NON_ELECTRICAL";
	})(HilModuleResolutionType || (hilModuleResolutionType.HilModuleResolutionType = HilModuleResolutionType = {}));
	hilModuleResolutionType.hilModuleResolutionTypeSchema = zod_1.z.nativeEnum(HilModuleResolutionType);
	
	return hilModuleResolutionType;
}

var customerReadOnlyBomItem = {};

var bomRowDatasheetStatus = {};

var bomDatasheetCoverage = {};

var customerBomReviewGrouping = {};

var manufacturerNames = {};

var hasRequiredManufacturerNames;

function requireManufacturerNames () {
	if (hasRequiredManufacturerNames) return manufacturerNames;
	hasRequiredManufacturerNames = 1;
	Object.defineProperty(manufacturerNames, "__esModule", { value: true });
	manufacturerNames.ManufacturerNames = void 0;
	class ManufacturerNames {
	    static { this.BUSINESS_TERMS = [
	        'inc',
	        'incorporated',
	        'corp',
	        'corporation',
	        'ltd',
	        'limited',
	        'llc',
	        'co',
	        'company',
	        'plc',
	        'group',
	        'international',
	        'intl',
	        'global',
	        'technologies',
	        'technology',
	        'tech',
	        'industries',
	        'industrial',
	        'ind',
	        'solutions',
	        'systems',
	        'gmbh',
	        'ag',
	        'semiconductors',
	        'semiconductor',
	    ]; }
	    static standardize(name) {
	        return ManufacturerNames.standardizedWords(name).join('');
	    }
	    static matchKey(name) {
	        const trimmed = name?.trim() ?? '';
	        if (trimmed === '') {
	            return '';
	        }
	        const standardized = ManufacturerNames.standardize(trimmed);
	        return standardized === '' ? trimmed.toLowerCase() : standardized;
	    }
	    static areConsistent(first, second) {
	        const a = ManufacturerNames.comparableWords(first);
	        const b = ManufacturerNames.comparableWords(second);
	        if (a.length === 0 || b.length === 0) {
	            return false;
	        }
	        if (a.join('') === b.join('')) {
	            return true;
	        }
	        const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
	        return shorter.every((word, index) => word === longer[index]);
	    }
	    static standardizedWords(name) {
	        let standardized = name.toLowerCase().trim();
	        standardized = standardized.replace(/[.,&]/g, ' ');
	        standardized = standardized.replace(/\s+/g, ' ');
	        for (const term of ManufacturerNames.BUSINESS_TERMS) {
	            const pattern = new RegExp(`\\b${term}\\b`);
	            standardized = standardized.replace(pattern, '');
	        }
	        return standardized.split(/\s+/).filter((word) => word.length > 0);
	    }
	    static comparableWords(name) {
	        return ManufacturerNames.standardizedWords(name)
	            .flatMap((word) => word.split(/[-/]/))
	            .filter((word) => word.length > 0);
	    }
	}
	manufacturerNames.ManufacturerNames = ManufacturerNames;
	
	return manufacturerNames;
}

var hasRequiredCustomerBomReviewGrouping;

function requireCustomerBomReviewGrouping () {
	if (hasRequiredCustomerBomReviewGrouping) return customerBomReviewGrouping;
	hasRequiredCustomerBomReviewGrouping = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.IMPORTANT_BOM_REVIEW_MIN_PINS = void 0;
		exports.resolveBomReviewGroupKey = resolveBomReviewGroupKey;
		exports.findBomReviewDatasheetSource = findBomReviewDatasheetSource;
		exports.resolveBomReviewDatasheetFileId = resolveBomReviewDatasheetFileId;
		exports.groupBomReviewItems = groupBomReviewItems;
		exports.isImportantBomReviewRow = isImportantBomReviewRow;
		exports.isBomReviewRowWithoutDatasheet = isBomReviewRowWithoutDatasheet;
		const manufacturer_names_1 = requireManufacturerNames();
		exports.IMPORTANT_BOM_REVIEW_MIN_PINS = 3;
		const PER_INSTANCE_METADATA_FIELDS = new Set(['reference']);
		function metadataFingerprint(metadata) {
		    return JSON.stringify(Object.entries(metadata)
		        .filter(([key]) => !PER_INSTANCE_METADATA_FIELDS.has(key))
		        .sort(([a], [b]) => a.localeCompare(b)));
		}
		function resolvePartKey(item) {
		    if (item.manufacturerPartNumber !== null && item.manufacturerPartNumber.trim() !== '') {
		        return item.manufacturerPartNumber;
		    }
		    if (item.staticMetadata.value !== null && item.staticMetadata.value.trim() !== '') {
		        const { value, numComponentPins, metadata } = item.staticMetadata;
		        return `__value__${value}__pins__${numComponentPins}__${metadataFingerprint(metadata)}`;
		    }
		    return `__null__${item.bomItemId}`;
		}
		function resolveBomReviewGroupKey(item) {
		    return [resolvePartKey(item), manufacturer_names_1.ManufacturerNames.matchKey(item.manufacturerName)].join('__');
		}
		function findBomReviewDatasheetSource(group) {
		    return (group.find((item) => (item.uploadedDatasheetFileId ?? null) !== null) ??
		        group.find((item) => item.datasheetFileId !== null));
		}
		function resolveBomReviewDatasheetFileId(group) {
		    const source = findBomReviewDatasheetSource(group);
		    return source?.uploadedDatasheetFileId ?? source?.datasheetFileId ?? null;
		}
		function groupBomReviewItems(items) {
		    const groups = new Map();
		    for (const item of items) {
		        const key = resolveBomReviewGroupKey(item);
		        const existing = groups.get(key);
		        if (existing !== undefined) {
		            existing.push(item);
		        }
		        else {
		            groups.set(key, [item]);
		        }
		    }
		    return groups;
		}
		function isImportantBomReviewRow(row) {
		    return row.numPins >= exports.IMPORTANT_BOM_REVIEW_MIN_PINS && row.datasheetFileId === null;
		}
		function isBomReviewRowWithoutDatasheet(row) {
		    return row.datasheetFileId === null;
		}
		
	} (customerBomReviewGrouping));
	return customerBomReviewGrouping;
}

var mechanicalNotAPart = {};

var hasRequiredMechanicalNotAPart;

function requireMechanicalNotAPart () {
	if (hasRequiredMechanicalNotAPart) return mechanicalNotAPart;
	hasRequiredMechanicalNotAPart = 1;
	Object.defineProperty(mechanicalNotAPart, "__esModule", { value: true });
	mechanicalNotAPart.MechanicalNotAPartRule = void 0;
	mechanicalNotAPart.resolveMechanicalNotAPartRule = resolveMechanicalNotAPartRule;
	mechanicalNotAPart.isMechanicalNotAPart = isMechanicalNotAPart;
	var MechanicalNotAPartRule;
	(function (MechanicalNotAPartRule) {
	    MechanicalNotAPartRule["MountingHole"] = "mounting_hole";
	    MechanicalNotAPartRule["Fiducial"] = "fiducial";
	    MechanicalNotAPartRule["NetTie"] = "net_tie";
	    MechanicalNotAPartRule["SolderJumper"] = "solder_jumper";
	    MechanicalNotAPartRule["BarePad"] = "bare_pad";
	})(MechanicalNotAPartRule || (mechanicalNotAPart.MechanicalNotAPartRule = MechanicalNotAPartRule = {}));
	const FEATURE_WORD_PATTERNS = [
	    { rule: MechanicalNotAPartRule.MountingHole, pattern: /(?:^|[^a-z0-9])mounting[\s_-]*hole(?!s)/i },
	    { rule: MechanicalNotAPartRule.Fiducial, pattern: /(?:^|[^a-z0-9])fiducial/i },
	    { rule: MechanicalNotAPartRule.NetTie, pattern: /(?:^|[^a-z0-9])net[\s_-]*tie/i },
	    { rule: MechanicalNotAPartRule.SolderJumper, pattern: /(?:^|[^a-z0-9])solder[\s_-]*jumper/i },
	];
	const PART_NAME_FIELD_PATTERNS = [
	    ...FEATURE_WORD_PATTERNS,
	    { rule: MechanicalNotAPartRule.SolderJumper, pattern: /(?:^|[^a-z0-9])jumper_\d+_bridged/i },
	    { rule: MechanicalNotAPartRule.BarePad, pattern: /^(?:[^:]*:)?(?:PAD|PTH)_/i },
	];
	const IDENTIFIER_FIELD_PATTERNS = [
	    ...PART_NAME_FIELD_PATTERNS,
	    { rule: MechanicalNotAPartRule.MountingHole, pattern: /(?:^|[^a-z0-9])MH(?:[^a-z0-9]|$)/i },
	];
	const FOOTPRINT_PATTERNS = [
	    { rule: MechanicalNotAPartRule.MountingHole, pattern: /^(?:[^:]*:)?mounting[\s_-]*hole/i },
	    { rule: MechanicalNotAPartRule.Fiducial, pattern: /^(?:[^:]*:)?fiducial/i },
	    { rule: MechanicalNotAPartRule.NetTie, pattern: /^(?:[^:]*:)?net[\s_-]*tie/i },
	    { rule: MechanicalNotAPartRule.SolderJumper, pattern: /^(?:[^:]*:)?solder[\s_-]*jumper/i },
	];
	const REFERENCE_DESIGNATOR_PREFIX_RULES = new Map([
	    ['MH', MechanicalNotAPartRule.MountingHole],
	    ['FID', MechanicalNotAPartRule.Fiducial],
	    ['NT', MechanicalNotAPartRule.NetTie],
	]);
	const SOLDER_JUMPER_REFERENCE_DESIGNATOR_PREFIX = 'JP';
	const SOLDER_JUMPER_LIBRARY_PATTERN = /(?:^|[^a-z0-9])solder[\s_-]*jumper/i;
	const IDENTIFIER_COLUMNS = new Set([
	    'designitemid',
	    'libref',
	    'libreference',
	    'libraryref',
	    'libraryreference',
	    'originalname',
	]);
	const PART_NAME_COLUMNS = new Set([
	    'value',
	    'comment',
	    'partname',
	    'componentname',
	    'symbolname',
	    'name',
	]);
	const LIBRARY_COLUMNS = new Set(['library', 'libraryname', 'sourcelibraryname', 'libid']);
	const DESCRIPTION_COLUMNS = new Set(['description', 'desc', 'kidescription']);
	function normalizeColumnName(column) {
	    return column.toLowerCase().replace(/[^a-z0-9]/g, '');
	}
	function matchPatterns(texts, patterns) {
	    for (const { rule, pattern } of patterns) {
	        if (texts.some((text) => pattern.test(text))) {
	            return rule;
	        }
	    }
	    return null;
	}
	function collectFieldTexts(fields) {
	    const identifiers = [];
	    const partNames = [];
	    const libraries = [];
	    const descriptions = [];
	    const footprints = [];
	    const value = fields.value?.trim() ?? '';
	    if (value !== '') {
	        partNames.push(value);
	    }
	    for (const [column, cell] of Object.entries(fields.metadata)) {
	        if (typeof cell !== 'string' || cell.trim() === '') {
	            continue;
	        }
	        const text = cell.trim();
	        const normalizedColumn = normalizeColumnName(column);
	        if (IDENTIFIER_COLUMNS.has(normalizedColumn)) {
	            identifiers.push(text);
	        }
	        else if (PART_NAME_COLUMNS.has(normalizedColumn)) {
	            partNames.push(text);
	        }
	        else if (LIBRARY_COLUMNS.has(normalizedColumn)) {
	            libraries.push(text);
	        }
	        else if (DESCRIPTION_COLUMNS.has(normalizedColumn)) {
	            descriptions.push(text);
	        }
	        else if (normalizedColumn.includes('footprint')) {
	            footprints.push(text);
	        }
	    }
	    return { identifiers, partNames, libraries, descriptions, footprints };
	}
	function matchReferenceDesignator(referenceDesignator, libraryTexts) {
	    const prefix = /^[a-z]+/i.exec(referenceDesignator?.trim() ?? '')?.[0]?.toUpperCase();
	    if (prefix === undefined) {
	        return null;
	    }
	    if (prefix === SOLDER_JUMPER_REFERENCE_DESIGNATOR_PREFIX) {
	        return libraryTexts.some((text) => SOLDER_JUMPER_LIBRARY_PATTERN.test(text)) ?
	            MechanicalNotAPartRule.SolderJumper
	            : null;
	    }
	    return REFERENCE_DESIGNATOR_PREFIX_RULES.get(prefix) ?? null;
	}
	function isRealPartNumber(partNumber) {
	    const trimmed = partNumber.trim();
	    if (trimmed === '' || /^(?:https?:\/\/|www\.)/i.test(trimmed)) {
	        return false;
	    }
	    return matchPatterns([trimmed], IDENTIFIER_FIELD_PATTERNS) === null;
	}
	function resolveMechanicalNotAPartRule(fields, partNumbers) {
	    const { identifiers, partNames, libraries, descriptions, footprints } = collectFieldTexts(fields);
	    const rule = matchPatterns(identifiers, IDENTIFIER_FIELD_PATTERNS) ??
	        matchPatterns(partNames, PART_NAME_FIELD_PATTERNS) ??
	        matchPatterns(descriptions, FEATURE_WORD_PATTERNS) ??
	        matchPatterns(footprints, FOOTPRINT_PATTERNS) ??
	        matchReferenceDesignator(fields.referenceDesignator, [...libraries, ...identifiers, ...partNames]);
	    if (rule === null || partNumbers().some(isRealPartNumber)) {
	        return null;
	    }
	    return rule;
	}
	function isMechanicalNotAPart(fields, partNumbers) {
	    return resolveMechanicalNotAPartRule(fields, partNumbers) !== null;
	}
	
	return mechanicalNotAPart;
}

var hasRequiredBomDatasheetCoverage;

function requireBomDatasheetCoverage () {
	if (hasRequiredBomDatasheetCoverage) return bomDatasheetCoverage;
	hasRequiredBomDatasheetCoverage = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.bomDatasheetCoverageSchema = exports.packageDatasheetGapRowCountsSchema = exports.PackageDatasheetGapReason = exports.DatasheetMissingReason = exports.DatasheetNotApplicableReason = exports.TEST_POINT_SIGMA_MODEL_TYPE = void 0;
		exports.resolveDatasheetNotApplicableReason = resolveDatasheetNotApplicableReason;
		exports.isDatasheetNotApplicable = isDatasheetNotApplicable;
		exports.resolveBomReviewRowDatasheetNotApplicableReason = resolveBomReviewRowDatasheetNotApplicableReason;
		exports.resolveBomReviewRowDatasheetMissingReason = resolveBomReviewRowDatasheetMissingReason;
		exports.resolvePackageDatasheetGapReason = resolvePackageDatasheetGapReason;
		exports.resolveBomReviewRowPackageDatasheetGapReason = resolveBomReviewRowPackageDatasheetGapReason;
		exports.summarizeBomDatasheetCoverage = summarizeBomDatasheetCoverage;
		const zod_1 = /*@__PURE__*/ requireZod();
		const package_datasheet_state_1 = requirePackageDatasheetState();
		const customer_bom_review_grouping_1 = requireCustomerBomReviewGrouping();
		const mechanical_not_a_part_1 = requireMechanicalNotAPart();
		exports.TEST_POINT_SIGMA_MODEL_TYPE = 'connector.test-point';
		var DatasheetNotApplicableReason;
		(function (DatasheetNotApplicableReason) {
		    DatasheetNotApplicableReason["Dnp"] = "dnp";
		    DatasheetNotApplicableReason["NotAPart"] = "not_a_part";
		})(DatasheetNotApplicableReason || (exports.DatasheetNotApplicableReason = DatasheetNotApplicableReason = {}));
		var DatasheetMissingReason;
		(function (DatasheetMissingReason) {
		    DatasheetMissingReason["PackageWithoutDatasheet"] = "package_without_datasheet";
		    DatasheetMissingReason["PartNotIdentified"] = "part_not_identified";
		})(DatasheetMissingReason || (exports.DatasheetMissingReason = DatasheetMissingReason = {}));
		var PackageDatasheetGapReason;
		(function (PackageDatasheetGapReason) {
		    PackageDatasheetGapReason["StillSearching"] = "still_searching";
		    PackageDatasheetGapReason["NothingFound"] = "nothing_found";
		    PackageDatasheetGapReason["AllCandidatesRejected"] = "all_candidates_rejected";
		    PackageDatasheetGapReason["NoSources"] = "no_sources";
		    PackageDatasheetGapReason["NotAssessed"] = "not_assessed";
		    PackageDatasheetGapReason["Other"] = "other";
		})(PackageDatasheetGapReason || (exports.PackageDatasheetGapReason = PackageDatasheetGapReason = {}));
		const rowCountSchema = zod_1.z.number().int().nonnegative();
		exports.packageDatasheetGapRowCountsSchema = zod_1.z.object({
		    [PackageDatasheetGapReason.StillSearching]: rowCountSchema,
		    [PackageDatasheetGapReason.NothingFound]: rowCountSchema,
		    [PackageDatasheetGapReason.AllCandidatesRejected]: rowCountSchema,
		    [PackageDatasheetGapReason.NoSources]: rowCountSchema,
		    [PackageDatasheetGapReason.NotAssessed]: rowCountSchema,
		    [PackageDatasheetGapReason.Other]: rowCountSchema,
		});
		exports.bomDatasheetCoverageSchema = zod_1.z.object({
		    expectedRowCount: rowCountSchema,
		    coveredRowCount: rowCountSchema,
		    missingPackageRowCount: rowCountSchema,
		    missingPackageRowCountsByReason: exports.packageDatasheetGapRowCountsSchema.optional(),
		    missingUnidentifiedRowCount: rowCountSchema,
		    dnpRowCount: rowCountSchema,
		    notAPartRowCount: rowCountSchema,
		});
		function resolveDatasheetNotApplicableReason(flags) {
		    if (flags.isVirtual ||
		        flags.isNonElectrical ||
		        flags.sigmaModelType === exports.TEST_POINT_SIGMA_MODEL_TYPE ||
		        (0, mechanical_not_a_part_1.isMechanicalNotAPart)(flags.staticMetadata, () => flags.manufacturerPartNumber === null ?
		            flags.metadataPartNumbers
		            : [flags.manufacturerPartNumber, ...flags.metadataPartNumbers])) {
		        return DatasheetNotApplicableReason.NotAPart;
		    }
		    if (flags.isDnp) {
		        return DatasheetNotApplicableReason.Dnp;
		    }
		    return null;
		}
		function isDatasheetNotApplicable(flags) {
		    return resolveDatasheetNotApplicableReason(flags) !== null;
		}
		function resolveBomReviewRowDatasheetNotApplicableReason(group) {
		    const reasons = group.map((item) => resolveDatasheetNotApplicableReason(item));
		    if (reasons.length === 0 || reasons.includes(null)) {
		        return null;
		    }
		    return reasons.includes(DatasheetNotApplicableReason.NotAPart) ?
		        DatasheetNotApplicableReason.NotAPart
		        : DatasheetNotApplicableReason.Dnp;
		}
		function resolveBomReviewRowDatasheetMissingReason(group) {
		    if (!(0, customer_bom_review_grouping_1.isBomReviewRowWithoutDatasheet)({ datasheetFileId: (0, customer_bom_review_grouping_1.resolveBomReviewDatasheetFileId)(group) })) {
		        return null;
		    }
		    return group.some((item) => item.packageId !== null) ?
		        DatasheetMissingReason.PackageWithoutDatasheet
		        : DatasheetMissingReason.PartNotIdentified;
		}
		function resolvePackageDatasheetGapReason(state, reason) {
		    if (state === null) {
		        return PackageDatasheetGapReason.NotAssessed;
		    }
		    if (state === package_datasheet_state_1.DatasheetState.Searching) {
		        return PackageDatasheetGapReason.StillSearching;
		    }
		    if (state !== package_datasheet_state_1.DatasheetState.NotFound) {
		        return PackageDatasheetGapReason.Other;
		    }
		    switch (reason) {
		        case package_datasheet_state_1.DatasheetStateReason.AllSourcesFailed:
		        case package_datasheet_state_1.DatasheetStateReason.AgentNotFound:
		            return PackageDatasheetGapReason.NothingFound;
		        case package_datasheet_state_1.DatasheetStateReason.AllCandidatesRejected:
		            return PackageDatasheetGapReason.AllCandidatesRejected;
		        case package_datasheet_state_1.DatasheetStateReason.NoSources:
		            return PackageDatasheetGapReason.NoSources;
		        default:
		            return PackageDatasheetGapReason.Other;
		    }
		}
		function resolveBomReviewRowPackageDatasheetGapReason(group) {
		    const bound = group.find((item) => item.packageId !== null);
		    if (bound === undefined) {
		        return null;
		    }
		    return resolvePackageDatasheetGapReason(bound.packageDatasheetState, bound.packageDatasheetStateReason);
		}
		function summarizeBomDatasheetCoverage(items) {
		    const byReason = {
		        [PackageDatasheetGapReason.StillSearching]: 0,
		        [PackageDatasheetGapReason.NothingFound]: 0,
		        [PackageDatasheetGapReason.AllCandidatesRejected]: 0,
		        [PackageDatasheetGapReason.NoSources]: 0,
		        [PackageDatasheetGapReason.NotAssessed]: 0,
		        [PackageDatasheetGapReason.Other]: 0,
		    };
		    const coverage = {
		        expectedRowCount: 0,
		        coveredRowCount: 0,
		        missingPackageRowCount: 0,
		        missingPackageRowCountsByReason: byReason,
		        missingUnidentifiedRowCount: 0,
		        dnpRowCount: 0,
		        notAPartRowCount: 0,
		    };
		    const rows = [
		        ...(0, customer_bom_review_grouping_1.groupBomReviewItems)(items.filter((item) => !item.isVirtual)).values(),
		        ...(0, customer_bom_review_grouping_1.groupBomReviewItems)(items.filter((item) => item.isVirtual)).values(),
		    ];
		    for (const row of rows) {
		        const notApplicableReason = resolveBomReviewRowDatasheetNotApplicableReason(row);
		        if (notApplicableReason === DatasheetNotApplicableReason.Dnp) {
		            coverage.dnpRowCount += 1;
		            continue;
		        }
		        if (notApplicableReason === DatasheetNotApplicableReason.NotAPart) {
		            coverage.notAPartRowCount += 1;
		            continue;
		        }
		        coverage.expectedRowCount += 1;
		        const missingReason = resolveBomReviewRowDatasheetMissingReason(row);
		        if (missingReason === null) {
		            coverage.coveredRowCount += 1;
		        }
		        else if (missingReason === DatasheetMissingReason.PackageWithoutDatasheet) {
		            coverage.missingPackageRowCount += 1;
		            byReason[resolveBomReviewRowPackageDatasheetGapReason(row) ?? PackageDatasheetGapReason.Other] += 1;
		        }
		        else {
		            coverage.missingUnidentifiedRowCount += 1;
		        }
		    }
		    return coverage;
		}
		
	} (bomDatasheetCoverage));
	return bomDatasheetCoverage;
}

var hasRequiredBomRowDatasheetStatus;

function requireBomRowDatasheetStatus () {
	if (hasRequiredBomRowDatasheetStatus) return bomRowDatasheetStatus;
	hasRequiredBomRowDatasheetStatus = 1;
	Object.defineProperty(bomRowDatasheetStatus, "__esModule", { value: true });
	bomRowDatasheetStatus.bomRowDatasheetStatusFieldsSchema = bomRowDatasheetStatus.BomRowDatasheetStatus = void 0;
	bomRowDatasheetStatus.resolveBomRowDatasheetStatus = resolveBomRowDatasheetStatus;
	bomRowDatasheetStatus.resolveBomReviewRowDatasheetStatus = resolveBomReviewRowDatasheetStatus;
	const zod_1 = /*@__PURE__*/ requireZod();
	const package_datasheet_state_1 = requirePackageDatasheetState();
	const bom_datasheet_coverage_1 = requireBomDatasheetCoverage();
	const customer_bom_review_grouping_1 = requireCustomerBomReviewGrouping();
	var BomRowDatasheetStatus;
	(function (BomRowDatasheetStatus) {
	    BomRowDatasheetStatus["Found"] = "found";
	    BomRowDatasheetStatus["Searching"] = "searching";
	    BomRowDatasheetStatus["NotFound"] = "not_found";
	    BomRowDatasheetStatus["NotApplicable"] = "not_applicable";
	    BomRowDatasheetStatus["Dnp"] = "dnp";
	    BomRowDatasheetStatus["NotAPart"] = "not_a_part";
	    BomRowDatasheetStatus["PartNotIdentified"] = "part_not_identified";
	})(BomRowDatasheetStatus || (bomRowDatasheetStatus.BomRowDatasheetStatus = BomRowDatasheetStatus = {}));
	bomRowDatasheetStatus.bomRowDatasheetStatusFieldsSchema = zod_1.z.object({
	    datasheetStatus: zod_1.z.nativeEnum(BomRowDatasheetStatus).nullable(),
	    datasheetStatusReason: zod_1.z.nativeEnum(package_datasheet_state_1.DatasheetStateReason).nullable(),
	    datasheetNextAttemptAt: zod_1.z.date().nullable(),
	});
	const NO_DATASHEET_STATUS = {
	    datasheetStatus: null,
	    datasheetStatusReason: null,
	    datasheetNextAttemptAt: null,
	};
	function resolveBomRowDatasheetStatus(input, now) {
	    if (input.shownDatasheetFileId !== null) {
	        return { ...NO_DATASHEET_STATUS, datasheetStatus: BomRowDatasheetStatus.Found };
	    }
	    const notApplicableReason = (0, bom_datasheet_coverage_1.resolveDatasheetNotApplicableReason)(input);
	    if (notApplicableReason !== null) {
	        return {
	            ...NO_DATASHEET_STATUS,
	            datasheetStatus: notApplicableReason === bom_datasheet_coverage_1.DatasheetNotApplicableReason.Dnp ?
	                BomRowDatasheetStatus.Dnp
	                : BomRowDatasheetStatus.NotAPart,
	        };
	    }
	    if (input.packageId === null) {
	        return { ...NO_DATASHEET_STATUS, datasheetStatus: BomRowDatasheetStatus.PartNotIdentified };
	    }
	    switch (input.packageDatasheet?.state ?? null) {
	        case package_datasheet_state_1.DatasheetState.Searching: {
	            const leaseEnd = input.packageDatasheet?.nextAttemptAt ?? null;
	            return leaseEnd !== null && leaseEnd.getTime() <= now.getTime() ?
	                NO_DATASHEET_STATUS
	                : { ...NO_DATASHEET_STATUS, datasheetStatus: BomRowDatasheetStatus.Searching };
	        }
	        case package_datasheet_state_1.DatasheetState.NotFound:
	            return {
	                datasheetStatus: BomRowDatasheetStatus.NotFound,
	                datasheetStatusReason: input.packageDatasheet?.reason ?? null,
	                datasheetNextAttemptAt: input.packageDatasheet?.nextAttemptAt ?? null,
	            };
	        case package_datasheet_state_1.DatasheetState.NotApplicable:
	            return {
	                ...NO_DATASHEET_STATUS,
	                datasheetStatus: BomRowDatasheetStatus.NotApplicable,
	                datasheetStatusReason: input.packageDatasheet?.reason ?? null,
	            };
	        default:
	            return NO_DATASHEET_STATUS;
	    }
	}
	function resolveBomReviewRowDatasheetStatus(group) {
	    if ((0, customer_bom_review_grouping_1.resolveBomReviewDatasheetFileId)(group) !== null) {
	        return { ...NO_DATASHEET_STATUS, datasheetStatus: BomRowDatasheetStatus.Found };
	    }
	    const isExempt = (item) => item.datasheetStatus === BomRowDatasheetStatus.Dnp || item.datasheetStatus === BomRowDatasheetStatus.NotAPart;
	    const expecting = group.filter((item) => !isExempt(item));
	    if (group.length > 0 && expecting.length === 0) {
	        return {
	            ...NO_DATASHEET_STATUS,
	            datasheetStatus: group.some((item) => item.datasheetStatus === BomRowDatasheetStatus.NotAPart) ?
	                BomRowDatasheetStatus.NotAPart
	                : BomRowDatasheetStatus.Dnp,
	        };
	    }
	    const source = expecting.find((item) => item.datasheetStatus !== null);
	    if (source === undefined) {
	        return NO_DATASHEET_STATUS;
	    }
	    return {
	        datasheetStatus: source.datasheetStatus,
	        datasheetStatusReason: source.datasheetStatusReason,
	        datasheetNextAttemptAt: source.datasheetNextAttemptAt,
	    };
	}
	
	return bomRowDatasheetStatus;
}

var hasRequiredCustomerReadOnlyBomItem;

function requireCustomerReadOnlyBomItem () {
	if (hasRequiredCustomerReadOnlyBomItem) return customerReadOnlyBomItem;
	hasRequiredCustomerReadOnlyBomItem = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.customerReadOnlyBomItemWithStaticMetadataSchema = exports.customerReadOnlyBomItemSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const customer_facing_bom_item_1 = requireCustomerFacingBomItem();
		const hil_module_resolution_type_1 = requireHilModuleResolutionType();
		const bom_row_datasheet_status_1 = requireBomRowDatasheetStatus();
		exports.customerReadOnlyBomItemSchema = zod_1.z.object({
		    bomItemId: zod_1.z.string(),
		    isDnp: zod_1.z.boolean(),
		    resolutionType: hil_module_resolution_type_1.hilModuleResolutionTypeSchema,
		    manufacturerPartNumber: zod_1.z.string().nullable(),
		    manufacturerId: zod_1.z.string().nullable(),
		    manufacturerName: zod_1.z.string().nullable(),
		    packageId: zod_1.z.string().nullable(),
		    sigmaModelType: zod_1.z.string().nullable(),
		    datasheetFileId: zod_1.z.string().nullable(),
		    datasheetIsConfidential: zod_1.z.boolean(),
		    ...bom_row_datasheet_status_1.bomRowDatasheetStatusFieldsSchema.shape,
		});
		exports.customerReadOnlyBomItemWithStaticMetadataSchema = exports.customerReadOnlyBomItemSchema.extend({
		    staticMetadata: customer_facing_bom_item_1.customerFacingBomItemStaticMetadataSchema,
		});
		
	} (customerReadOnlyBomItem));
	return customerReadOnlyBomItem;
}

var customerEditableBomItem = {};

var hasRequiredCustomerEditableBomItem;

function requireCustomerEditableBomItem () {
	if (hasRequiredCustomerEditableBomItem) return customerEditableBomItem;
	hasRequiredCustomerEditableBomItem = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.customerEditableBomItemUpdateDtoSchema = exports.customerEditableBomItemWithStaticMetadataSchema = exports.customerEditableBomItemSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const zod_partial_1 = requireZodPartial();
		const customer_facing_bom_item_1 = requireCustomerFacingBomItem();
		const customer_read_only_bom_item_1 = requireCustomerReadOnlyBomItem();
		const catalogue_item_1 = requireCatalogueItem();
		exports.customerEditableBomItemSchema = customer_read_only_bom_item_1.customerReadOnlyBomItemSchema.extend({
		    isCustomerReviewed: zod_1.z.boolean(),
		    foundInCatalogue: catalogue_item_1.CatalogueNameSchema.nullable(),
		    manufacturerNameMetadataSourceField: zod_1.z.string().nullable(),
		    manufacturerPartNumberMetadataSourceField: zod_1.z.string().nullable(),
		    isDnpMetadataSourceField: zod_1.z.string().nullable(),
		    isPrefilledFromMetadata: zod_1.z.boolean(),
		    uploadedDatasheetFileId: zod_1.z.string().nullable(),
		});
		exports.customerEditableBomItemWithStaticMetadataSchema = exports.customerEditableBomItemSchema.extend({
		    staticMetadata: customer_facing_bom_item_1.customerFacingBomItemStaticMetadataSchema,
		});
		exports.customerEditableBomItemUpdateDtoSchema = (0, zod_partial_1.partialWithoutDefaults)(exports.customerEditableBomItemSchema
		    .omit({
		    bomItemId: true,
		    datasheetIsConfidential: true,
		    datasheetStatus: true,
		    datasheetStatusReason: true,
		    datasheetNextAttemptAt: true,
		    isPrefilledFromMetadata: true,
		    manufacturerNameMetadataSourceField: true,
		    manufacturerPartNumberMetadataSourceField: true,
		    uploadedDatasheetFileId: true,
		})
		    .extend({ uploadedDatasheetFileId: zod_1.z.null() }));
		
	} (customerEditableBomItem));
	return customerEditableBomItem;
}

var tag = {};

var hasRequiredTag;

function requireTag () {
	if (hasRequiredTag) return tag;
	hasRequiredTag = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.TagSchema = exports.TagIdentifierSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.TagIdentifierSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    name: zod_1.z.string(),
		});
		exports.TagSchema = exports.TagIdentifierSchema.extend({
		    createdAt: zod_1.z.coerce.date(),
		    updatedAt: zod_1.z.coerce.date(),
		});
		
	} (tag));
	return tag;
}

var userManagement_types = {};

var hasRequiredUserManagement_types;

function requireUserManagement_types () {
	if (hasRequiredUserManagement_types) return userManagement_types;
	hasRequiredUserManagement_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.MembershipWithRelationsSchema = exports.MembershipWithGroupSchema = exports.MembershipSchema = exports.OrganizationSchema = exports.UserSchema = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		const index_js_1 = requireAuth();
		exports.UserSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    username: zod_1.z.string(),
		    email: zod_1.z.string().nullable(),
		    displayName: zod_1.z.string().nullable(),
		    type: zod_1.z.nativeEnum(index_js_1.UserRecordType),
		    auth0UserId: zod_1.z.string().nullable(),
		    emailConfirmed: zod_1.z.boolean(),
		    createdAt: zod_1.z.coerce.date(),
		});
		exports.OrganizationSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    organizationName: zod_1.z.string(),
		    displayName: zod_1.z.string().nullable(),
		    createdAt: zod_1.z.coerce.date(),
		});
		exports.MembershipSchema = zod_1.z.object({
		    id: zod_1.z.string(),
		    memberId: zod_1.z.string(),
		    groupId: zod_1.z.string(),
		    role: zod_1.z.nativeEnum(index_js_1.MembershipRole),
		    createdAt: zod_1.z.coerce.date(),
		});
		exports.MembershipWithGroupSchema = exports.MembershipSchema.extend({
		    group: exports.OrganizationSchema,
		});
		exports.MembershipWithRelationsSchema = exports.MembershipSchema.extend({
		    member: exports.UserSchema,
		    group: exports.OrganizationSchema,
		});
		
	} (userManagement_types));
	return userManagement_types;
}

var inviteState = {};

var hasRequiredInviteState;

function requireInviteState () {
	if (hasRequiredInviteState) return inviteState;
	hasRequiredInviteState = 1;
	Object.defineProperty(inviteState, "__esModule", { value: true });
	inviteState.isInvitePending = isInvitePending;
	function isInvitePending(user) {
	    return user.auth0UserId !== null && !user.emailConfirmed;
	}
	
	return inviteState;
}

var documentAcceptance_types = {};

var hasRequiredDocumentAcceptance_types;

function requireDocumentAcceptance_types () {
	if (hasRequiredDocumentAcceptance_types) return documentAcceptance_types;
	hasRequiredDocumentAcceptance_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.AUTH0_COMPLIANCE_STATUS_CLAIM = exports.ComplianceStatusClaimSchema = exports.RecordAcceptanceOutputSchema = exports.RecordAcceptanceInputSchema = exports.CheckDocumentComplianceOutputSchema = exports.CheckDocumentComplianceOrganizationPayloadSchema = exports.CheckDocumentComplianceInputSchema = exports.OrganizationDocumentStatusSchema = exports.DocumentComplianceStatusSchema = exports.OrganizationComplianceStateSchema = exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS = exports.DocumentRegistrySchema = exports.DocumentTypeEntrySchema = exports.DocumentVariantEntrySchema = exports.DocumentVersionEntrySchema = exports.DocumentVersionIdSchema = exports.DocumentVariantIdSchema = exports.DocumentTypeSchema = void 0;
		exports.buildDocumentVersionId = buildDocumentVersionId;
		exports.parseDocumentVersionId = parseDocumentVersionId;
		exports.getCurrentDocumentVersion = getCurrentDocumentVersion;
		exports.isVersionValidForWrite = isVersionValidForWrite;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.DocumentTypeSchema = zod_1.z.enum(['tos', 'privacyPolicy']);
		exports.DocumentVariantIdSchema = zod_1.z
		    .string()
		    .regex(/^(exempt|[a-z][a-z0-9_]{1,63})$/)
		    .max(255);
		exports.DocumentVersionIdSchema = zod_1.z
		    .string()
		    .regex(/^(tos|privacyPolicy):[a-z][a-z0-9_]{1,63}:\d{4}-\d{2}-\d{2}-(v\d+|draft)$/, {
		    message: 'DocumentVersionId must be `{type}:{variant}:{version}` where version is `YYYY-MM-DD-vN` or `YYYY-MM-DD-draft` (e.g., tos:canada:2026-04-15-v1 or tos:canada:2026-04-27-draft)',
		})
		    .max(255);
		function buildDocumentVersionId(type, variant, version) {
		    return `${type}:${variant}:${version}`;
		}
		function parseDocumentVersionId(id) {
		    const parts = id.split(':');
		    if (parts.length !== 3) {
		        throw new Error(`Invalid DocumentVersionId '${id}': expected 3 colon-separated parts`);
		    }
		    const [typeRaw, variant, version] = parts;
		    if (typeRaw !== 'tos' && typeRaw !== 'privacyPolicy') {
		        throw new Error(`Invalid DocumentVersionId '${id}': unknown document type '${typeRaw}'`);
		    }
		    return { type: typeRaw, variant, version };
		}
		exports.DocumentVersionEntrySchema = zod_1.z.object({
		    id: exports.DocumentVersionIdSchema,
		    version: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}-(v\d+|draft)$/),
		    effectiveAt: zod_1.z.string().datetime(),
		    publishedAt: zod_1.z.string().datetime(),
		    url: zod_1.z.string().url(),
		    pdfUrl: zod_1.z.string().url().optional(),
		    webUrl: zod_1.z.string().url().optional(),
		    mdChecksum: zod_1.z.string().regex(/^sha256:[a-f0-9]{64}$/),
		    pdfChecksum: zod_1.z.string().regex(/^sha256:[a-f0-9]{64}$/),
		    gracePeriodDays: zod_1.z.number().int().min(0),
		    changelog: zod_1.z.string().optional(),
		});
		exports.DocumentVariantEntrySchema = zod_1.z.object({
		    displayName: zod_1.z.string().min(1),
		    versions: zod_1.z.array(exports.DocumentVersionEntrySchema).min(1),
		});
		exports.DocumentTypeEntrySchema = zod_1.z
		    .object({
		    displayName: zod_1.z.string().min(1),
		    defaultVariant: exports.DocumentVariantIdSchema,
		    variants: zod_1.z.record(zod_1.z.string().regex(/^[a-z][a-z0-9_]{1,63}$/), exports.DocumentVariantEntrySchema),
		})
		    .refine((v) => v.variants[v.defaultVariant] !== undefined, {
		    message: '`defaultVariant` must reference a variant listed in `variants`',
		});
		exports.DocumentRegistrySchema = zod_1.z.object({
		    schemaVersion: zod_1.z.literal('1'),
		    updatedAt: zod_1.z.string().datetime(),
		    documents: zod_1.z.object({
		        tos: exports.DocumentTypeEntrySchema,
		        privacyPolicy: exports.DocumentTypeEntrySchema,
		    }),
		});
		exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS = 60 * 60 * 1000;
		function getCurrentDocumentVersion(variant, now = new Date()) {
		    const nowMs = now.getTime();
		    const effectiveEntries = variant.versions.filter((v) => new Date(v.effectiveAt).getTime() <= nowMs);
		    if (effectiveEntries.length === 0) {
		        return null;
		    }
		    return effectiveEntries.reduce((newest, candidate) => new Date(candidate.effectiveAt).getTime() > new Date(newest.effectiveAt).getTime() ? candidate : newest);
		}
		function isVersionValidForWrite(version, now = new Date()) {
		    const effectiveMs = new Date(version.effectiveAt).getTime();
		    return effectiveMs - exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS <= now.getTime();
		}
		exports.OrganizationComplianceStateSchema = zod_1.z.object({
		    applicableTosVariant: exports.DocumentVariantIdSchema,
		    applicablePrivacyVariant: exports.DocumentVariantIdSchema,
		    tosAcceptedId: exports.DocumentVersionIdSchema.nullable(),
		    tosAcceptedDate: zod_1.z.string().datetime().nullable(),
		    tosAcceptedByUserId: zod_1.z.string().nullable(),
		    privacyAcceptedId: exports.DocumentVersionIdSchema.nullable(),
		    privacyAcceptedDate: zod_1.z.string().datetime().nullable(),
		    privacyAcceptedByUserId: zod_1.z.string().nullable(),
		});
		exports.DocumentComplianceStatusSchema = zod_1.z.enum([
		    'compliant',
		    'grace_period',
		    'blocked',
		]);
		exports.OrganizationDocumentStatusSchema = zod_1.z.object({
		    state: exports.DocumentComplianceStatusSchema,
		    gracePeriodEnd: zod_1.z.string().datetime().nullable(),
		    latestVersionId: exports.DocumentVersionIdSchema.nullable(),
		    latestVersionUrl: zod_1.z.string().url().nullable(),
		    latestVersionName: zod_1.z.string().nullable(),
		    acceptedVersionId: exports.DocumentVersionIdSchema.nullable(),
		});
		exports.CheckDocumentComplianceInputSchema = zod_1.z.object({
		    auth0UserId: zod_1.z.string().min(1),
		    auth0OrganizationId: zod_1.z.string().optional(),
		});
		exports.CheckDocumentComplianceOrganizationPayloadSchema = zod_1.z.object({
		    cadstromOrganizationId: zod_1.z.string(),
		    displayName: zod_1.z.string(),
		    role: zod_1.z.enum(['admin', 'member', 'trainee']),
		    canAcceptTos: zod_1.z.boolean(),
		    canAcceptPrivacy: zod_1.z.boolean(),
		    compliance: exports.OrganizationComplianceStateSchema,
		    status: zod_1.z.object({
		        tos: exports.OrganizationDocumentStatusSchema,
		        privacy: exports.OrganizationDocumentStatusSchema,
		    }),
		});
		exports.CheckDocumentComplianceOutputSchema = zod_1.z.object({
		    cadstromUserId: zod_1.z.string(),
		    organization: exports.CheckDocumentComplianceOrganizationPayloadSchema.nullable(),
		});
		exports.RecordAcceptanceInputSchema = zod_1.z.object({
		    auth0UserId: zod_1.z.string().min(1),
		    cadstromOrganizationId: zod_1.z.string().min(1),
		    tosAcceptedId: exports.DocumentVersionIdSchema.nullable().optional(),
		    privacyAcceptedId: exports.DocumentVersionIdSchema.nullable().optional(),
		});
		exports.RecordAcceptanceOutputSchema = zod_1.z.object({
		    cadstromOrganizationId: zod_1.z.string(),
		    compliance: exports.OrganizationComplianceStateSchema,
		});
		exports.ComplianceStatusClaimSchema = zod_1.z.object({
		    tos: exports.DocumentComplianceStatusSchema,
		    privacy: exports.DocumentComplianceStatusSchema,
		    tos_grace_period_end: zod_1.z.string().datetime().nullable(),
		    privacy_grace_period_end: zod_1.z.string().datetime().nullable(),
		    tos_latest_id: exports.DocumentVersionIdSchema.nullable(),
		    privacy_latest_id: exports.DocumentVersionIdSchema.nullable(),
		    tos_url: zod_1.z.string().url().nullable(),
		    privacy_url: zod_1.z.string().url().nullable(),
		});
		exports.AUTH0_COMPLIANCE_STATUS_CLAIM = 'https://cadstrom.io/compliance_status';
		
	} (documentAcceptance_types));
	return documentAcceptance_types;
}

var internalFiles_types = {};

var hasRequiredInternalFiles_types;

function requireInternalFiles_types () {
	if (hasRequiredInternalFiles_types) return internalFiles_types;
	hasRequiredInternalFiles_types = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.GetPresignedUploadUrlsOutputSchema = exports.PresignedUploadSchema = exports.GetPresignedUploadUrlsInputSchema = exports.PresignedUploadItemSchema = exports.PRESIGNED_UPLOAD_URLS_MAX_ITEMS = exports.GetPresignedDownloadUrlOutputSchema = exports.GetPresignedDownloadUrlInputSchema = exports.PRESIGNED_DOWNLOAD_URL_DEFAULT_EXPIRY_SECONDS = exports.PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS = void 0;
		const zod_1 = /*@__PURE__*/ requireZod();
		exports.PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS = 900;
		exports.PRESIGNED_DOWNLOAD_URL_DEFAULT_EXPIRY_SECONDS = 300;
		exports.GetPresignedDownloadUrlInputSchema = zod_1.z.object({
		    location: zod_1.z.string().min(1).max(2048),
		    grant: zod_1.z.string().min(1).max(16384),
		    expirySeconds: zod_1.z.number().int().min(1).max(exports.PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS).optional(),
		});
		exports.GetPresignedDownloadUrlOutputSchema = zod_1.z.object({
		    url: zod_1.z.string().url(),
		    expiresAt: zod_1.z.string().datetime(),
		});
		exports.PRESIGNED_UPLOAD_URLS_MAX_ITEMS = 64;
		exports.PresignedUploadItemSchema = zod_1.z.object({
		    location: zod_1.z.string().min(1).max(2048),
		    contentType: zod_1.z.string().min(1).max(256),
		});
		exports.GetPresignedUploadUrlsInputSchema = zod_1.z.object({
		    items: exports.PresignedUploadItemSchema.array().min(1).max(exports.PRESIGNED_UPLOAD_URLS_MAX_ITEMS),
		    grant: zod_1.z.string().min(1).max(16384),
		    expirySeconds: zod_1.z.number().int().min(1).max(exports.PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS).optional(),
		});
		exports.PresignedUploadSchema = zod_1.z.object({
		    location: zod_1.z.string(),
		    url: zod_1.z.string().url(),
		    headers: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
		});
		exports.GetPresignedUploadUrlsOutputSchema = zod_1.z.object({
		    uploads: exports.PresignedUploadSchema.array(),
		    expiresAt: zod_1.z.string().datetime(),
		});
		
	} (internalFiles_types));
	return internalFiles_types;
}

var ExpressionComparator = {};

var hasRequiredExpressionComparator;

function requireExpressionComparator () {
	if (hasRequiredExpressionComparator) return ExpressionComparator;
	hasRequiredExpressionComparator = 1;
	Object.defineProperty(ExpressionComparator, "__esModule", { value: true });
	ExpressionComparator.ExpressionComparator = void 0;
	class ExpressionComparator$1 {
	    static standardizeExpression(expression) {
	        if (!expression || expression.trim() === '') {
	            return '';
	        }
	        let normalized = expression
	            .replace(/(\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\s*\*\s*([A-Za-z_][A-Za-z0-9_]*)/g, '$2*$1')
	            .replace(/([A-Za-z_][A-Za-z0-9_]*)\s*\*\s*(\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g, '$1*$2')
	            .replace(/([A-Za-z_][A-Za-z0-9_]*)\s*\/\s*(\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g, '$1/$2');
	        const terms = [];
	        const parts = normalized.split(/(\s*[+-]\s*)/);
	        let currentOperator = '+';
	        for (const part of parts) {
	            const trimmed = part.trim();
	            if (trimmed === '+' || trimmed === '-') {
	                currentOperator = trimmed;
	            }
	            else if (trimmed) {
	                terms.push({ value: trimmed, operator: currentOperator });
	                currentOperator = '+';
	            }
	        }
	        const isNumber = (term) => /^\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(term);
	        const positiveNumbers = terms
	            .filter((t) => t.operator === '+' && isNumber(t.value))
	            .sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
	        const negativeNumbers = terms
	            .filter((t) => t.operator === '-' && isNumber(t.value))
	            .sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
	        const positiveVariables = terms
	            .filter((t) => t.operator === '+' && !isNumber(t.value))
	            .sort((a, b) => a.value.localeCompare(b.value));
	        const negativeVariables = terms
	            .filter((t) => t.operator === '-' && !isNumber(t.value))
	            .sort((a, b) => a.value.localeCompare(b.value));
	        const sortedTerms = [...positiveNumbers, ...negativeNumbers, ...positiveVariables, ...negativeVariables];
	        let result = '';
	        for (let i = 0; i < sortedTerms.length; i++) {
	            const term = sortedTerms[i];
	            if (i === 0) {
	                result += term.operator === '-' ? `-${term.value}` : term.value;
	            }
	            else {
	                result += ` ${term.operator} ${term.value}`;
	            }
	        }
	        return result;
	    }
	    static compareExpressions(exp1, exp2) {
	        return this.standardizeExpression(exp1) === this.standardizeExpression(exp2);
	    }
	}
	ExpressionComparator.ExpressionComparator = ExpressionComparator$1;
	
	return ExpressionComparator;
}

var Urls = {};

var packageStoreDatasheetUrl = {};

var hasRequiredPackageStoreDatasheetUrl;

function requirePackageStoreDatasheetUrl () {
	if (hasRequiredPackageStoreDatasheetUrl) return packageStoreDatasheetUrl;
	hasRequiredPackageStoreDatasheetUrl = 1;
	Object.defineProperty(packageStoreDatasheetUrl, "__esModule", { value: true });
	packageStoreDatasheetUrl.buildPackageStoreDatasheetUrl = buildPackageStoreDatasheetUrl;
	function buildPackageStoreDatasheetUrl(applicationBaseUrl, packageFamilyId, packageId) {
	    return `${applicationBaseUrl}/package-store/${packageFamilyId}/packages/${packageId}/datasheet`;
	}
	
	return packageStoreDatasheetUrl;
}

var protocolRelativeUrl = {};

var hasRequiredProtocolRelativeUrl;

function requireProtocolRelativeUrl () {
	if (hasRequiredProtocolRelativeUrl) return protocolRelativeUrl;
	hasRequiredProtocolRelativeUrl = 1;
	Object.defineProperty(protocolRelativeUrl, "__esModule", { value: true });
	protocolRelativeUrl.toAbsoluteUrl = toAbsoluteUrl;
	function toAbsoluteUrl(url) {
	    if (url === null || url === undefined) {
	        return undefined;
	    }
	    if (url.startsWith('//')) {
	        return `https:${url}`;
	    }
	    return /^www\./i.test(url) ? `https://${url}` : url;
	}
	
	return protocolRelativeUrl;
}

var hasRequiredUrls;

function requireUrls () {
	if (hasRequiredUrls) return Urls;
	hasRequiredUrls = 1;
	(function (exports) {
		var __createBinding = (Urls && Urls.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (Urls && Urls.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		__exportStar(requirePackageStoreDatasheetUrl(), exports);
		__exportStar(requireProtocolRelativeUrl(), exports);
		
	} (Urls));
	return Urls;
}

var hasRequiredDist;

function requireDist () {
	if (hasRequiredDist) return dist$2;
	hasRequiredDist = 1;
	(function (exports) {
		var __createBinding = (dist$2 && dist$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (dist$2 && dist$2.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.isBomReadyOrLaterStatus = exports.isActiveProcessingStatus = exports.BUILD_STATUSES = exports.isBuildStatus = exports.buildTimeoutReasonSchema = exports.BuildTimeoutReason = exports.buildStatusSchema = exports.BuildStatus = exports.pinoutValidationResultSchema = exports.packageOperatorReviewStatusSchema = exports.PackageOperatorReviewStatus = exports.packageStatusSchema = exports.PackageStatus = exports.NET_DRIVE_RESULT_DRIVE_MODES = exports.isNetDriveResultDriveMode = exports.NetDriveResultDriveMode = exports.validateFile = exports.Wizard = exports.Utils = exports.Templating = exports.NetlistValidation = exports.Events = exports.ModelText = exports.SignalTies = exports.SignalMapping = exports.ServiceLocations = exports.RequirementsV2 = exports.QueryBuilder = exports.ProjectModel = exports.PackageStore = exports.PackageEvidence = exports.PackageDataSpec = exports.PackageDataAdjudication = exports.ParentDeployment = exports.Parse = exports.ImplementationRules = exports.GingersnapContracts = exports.Gingersnap = exports.DnpColumns = exports.Consensus = exports.ComponentInferenceCsv = exports.Comparison = exports.CadstromConfig = exports.BlockDiagram = exports.Billing = exports.Auth = exports.AnalysisResult = exports.Analysis = exports.AgenticExtraction = exports.AgentChat = void 0;
		exports.PipelineOpsUnlistedReason = exports.PipelineOpsExclusion = exports.PipelineOpsAction = exports.PipelineNetlistFailure = exports.PipelineAwaitingState = exports.PIPELINE_WORKFLOW_CLOSED_STATUSES = exports.PIPELINE_OPS_UNLISTED_RUN_LIMIT = exports.DeliveryHealthReason = exports.PIPELINE_STAGE_BY_BUILD_STATUS = exports.PIPELINE_STAGES = exports.PIPELINE_OPS_MAX_WINDOW_DAYS = exports.PIPELINE_OPS_INTERNAL_BILLING_CLASS = exports.PIPELINE_OPS_EXCLUSIONS = exports.PIPELINE_OPS_DEFAULT_WINDOW_DAYS = exports.PIPELINE_AWAITING_STATE_BY_ATTRIBUTE = exports.NETLIST_WAIT_EXPIRED_WORKFLOW_STAGE = exports.AiAnalysisRecoveryMode = exports.AiAnalysisRecoveryBlockerKind = exports.ZodJsonSchemaTarget = exports.ZodJsonSchema = exports.JsonValueSchema = exports.isBuildStatusActor = exports.isBuildAutomationMode = exports.isAutomationStepEnabled = exports.buildAutomationModeSchema = exports.FAST_MODE_ANALYSIS_RUN_ID_PREFIX = exports.BuildStatusActor = exports.BuildEnrichmentArm = exports.BuildAutomationStep = exports.BuildAutomationMode = exports.AnalysisDispatchClaimOutcome = exports.ANALYSIS_OFF_RAMP_REASON_PREFIX = exports.ANALYSIS_FAILED_STATUS_MESSAGE = exports.AI_ANALYSIS_RECOVERY_FINDINGS_RUN_ID_PREFIX = exports.AI_ANALYSIS_INCOMPLETE_REASON_PREFIX = exports.TIMED_OUT_PREDECESSORS_BY_REASON = exports.isBoundForAnalysis = exports.APPROACHING_ANALYSIS_BUILD_STATUSES = exports.canBeginBomMerge = exports.POST_REVIEW_ERROR_ORIGIN_STATUSES = exports.isPreReviewBuildStatus = exports.PRE_REVIEW_BUILD_STATUSES = exports.isAnalyzableBuildStatus = exports.ANALYZABLE_BUILD_STATUSES = exports.isTerminalBuildStatus = exports.legalPredecessorsOf = exports.canTransition = exports.TransitionOutcome = exports.BUILD_STATUS_TRANSITIONS = exports.isFinalBuildStatus = void 0;
		exports.buildFileSchema = exports.buildSchema = exports.revisionFileSchema = exports.revisionStatusSchema = exports.revisionSchema = exports.revisionWithFilesSchema = exports.MAX_CATALOGUE_PICK_ROWS = exports.CatalogueDatasheetAttachOutcome = exports.isWorkflowStartStatus = exports.workflowStartStatusSchema = exports.WorkflowStartStatus = exports.analysisGateDispatchSchema = exports.AnalysisGateDispatch = exports.WorkflowStartOutcome = exports.restartProcessingOutcomeSchema = exports.RestartProcessingOutcome = exports.RemediationOwner = exports.ProjectParseRemediation = exports.ProjectParseFailureCode = exports.NetlistGenerationRemediation = exports.NetlistGenerationFailureCode = exports.NetlistTriagePointerSchema = exports.NetlistTriageLocations = exports.NetlistSource = exports.NetlistGateState = exports.NetlistClearanceOutcome = exports.isPipelinePhase = exports.ReviewerRoleDomainEventType = exports.ReviewerRoleChangePayloadSchema = exports.PipelinePhase = exports.DomainEventSubjectType = exports.BuildDomainEventType = exports.isDeliverySlaSource = exports.isDeliverySlaJudgment = exports.isDeliveryHealthReason = exports.isDeliveryFunnelGate = exports.isDeliveryDatasheetCoverageJudgment = exports.deliveryHealthSeverity = exports.DeliverySlaSource = exports.DeliverySlaJudgment = exports.DeliveryFunnelGate = exports.DeliveryDatasheetCoverageJudgment = exports.DELIVERY_HEALTH_SEVERITY = exports.isPipelineOpsExclusion = exports.pipelineWorkflowStatusFromTemporal = exports.pipelineStageRank = exports.pipelineAwaitingStateFromAttribute = exports.PipelineWorkflowStatus = exports.PipelineStageState = exports.PipelineStage = void 0;
		exports.UserSchema = exports.TagIdentifierSchema = exports.TagSchema = exports.pickPrimaryCatalogueName = exports.getPrimaryCatalogueItem = exports.CATALOGUE_PRIORITY_ORDER_QUOTA_CONSERVATIVE = exports.CATALOGUE_PRIORITY_ORDER = exports.CatalogueItemMetadataSchema = exports.CatalogueItemSchema = exports.CatalogueMatchKindSchema = exports.CatalogueMatchKind = exports.CatalogueNameSchema = exports.CatalogueName = exports.resolveBomRowDatasheetStatus = exports.resolveBomReviewRowDatasheetStatus = exports.bomRowDatasheetStatusFieldsSchema = exports.BomRowDatasheetStatus = exports.resolveMechanicalNotAPartRule = exports.isMechanicalNotAPart = exports.MechanicalNotAPartRule = exports.summarizeBomDatasheetCoverage = exports.resolvePackageDatasheetGapReason = exports.resolveDatasheetNotApplicableReason = exports.resolveBomReviewRowPackageDatasheetGapReason = exports.resolveBomReviewRowDatasheetNotApplicableReason = exports.resolveBomReviewRowDatasheetMissingReason = exports.packageDatasheetGapRowCountsSchema = exports.isDatasheetNotApplicable = exports.bomDatasheetCoverageSchema = exports.TEST_POINT_SIGMA_MODEL_TYPE = exports.PackageDatasheetGapReason = exports.DatasheetNotApplicableReason = exports.DatasheetMissingReason = exports.resolveBomReviewGroupKey = exports.resolveBomReviewDatasheetFileId = exports.isImportantBomReviewRow = exports.isBomReviewRowWithoutDatasheet = exports.groupBomReviewItems = exports.findBomReviewDatasheetSource = exports.IMPORTANT_BOM_REVIEW_MIN_PINS = exports.ManufacturerNames = exports.customerEditableBomItemUpdateDtoSchema = exports.customerEditableBomItemWithStaticMetadataSchema = exports.customerEditableBomItemSchema = exports.customerReadOnlyBomItemWithStaticMetadataSchema = exports.customerReadOnlyBomItemSchema = exports.hilModuleResolutionTypeSchema = exports.HilModuleResolutionType = exports.customerFacingBomItemStaticMetadataSchema = exports.buildWithFilesSchema = void 0;
		exports.ExpressionComparator = exports.PRESIGNED_UPLOAD_URLS_MAX_ITEMS = exports.PRESIGNED_DOWNLOAD_URL_DEFAULT_EXPIRY_SECONDS = exports.PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS = exports.PresignedUploadSchema = exports.PresignedUploadItemSchema = exports.GetPresignedUploadUrlsOutputSchema = exports.GetPresignedUploadUrlsInputSchema = exports.GetPresignedDownloadUrlOutputSchema = exports.GetPresignedDownloadUrlInputSchema = exports.isVersionValidForWrite = exports.getCurrentDocumentVersion = exports.parseDocumentVersionId = exports.buildDocumentVersionId = exports.AUTH0_COMPLIANCE_STATUS_CLAIM = exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS = exports.ComplianceStatusClaimSchema = exports.RecordAcceptanceOutputSchema = exports.RecordAcceptanceInputSchema = exports.CheckDocumentComplianceOutputSchema = exports.CheckDocumentComplianceOrganizationPayloadSchema = exports.CheckDocumentComplianceInputSchema = exports.OrganizationDocumentStatusSchema = exports.DocumentComplianceStatusSchema = exports.OrganizationComplianceStateSchema = exports.DocumentRegistrySchema = exports.DocumentTypeEntrySchema = exports.DocumentVariantEntrySchema = exports.DocumentVersionEntrySchema = exports.DocumentVersionIdSchema = exports.DocumentVariantIdSchema = exports.DocumentTypeSchema = exports.isInvitePending = exports.MembershipWithRelationsSchema = exports.MembershipWithGroupSchema = exports.MembershipSchema = exports.OrganizationSchema = void 0;
		exports.AgentChat = requireAgentChat();
		exports.AgenticExtraction = requireAgenticExtraction();
		exports.Analysis = requireAnalysis();
		exports.AnalysisResult = requireAnalysisResult();
		exports.Auth = requireAuth();
		exports.Billing = requireBilling();
		exports.BlockDiagram = requireBlockDiagram();
		exports.CadstromConfig = requireCadstromConfig();
		exports.Comparison = requireComparison();
		exports.ComponentInferenceCsv = requireComponentInferenceCsv();
		exports.Consensus = requireConsensus();
		exports.DnpColumns = requireDnpColumns();
		exports.Gingersnap = requireGingersnap$1();
		exports.GingersnapContracts = requireGingersnap();
		exports.ImplementationRules = requireImplementationRules();
		__exportStar(requireRuleNormalization(), exports);
		__exportStar(requireGenerationEvent(), exports);
		exports.Parse = requireParse();
		exports.ParentDeployment = requireParentDeployment();
		exports.PackageDataAdjudication = requirePackageDataAdjudication();
		exports.PackageDataSpec = requirePackageDataSpec();
		exports.PackageEvidence = requirePackageEvidence();
		exports.PackageStore = requirePackageStore();
		__exportStar(requirePackageStorePackageResolution(), exports);
		__exportStar(requirePackageRecordMetadata(), exports);
		__exportStar(requireParentRatingRow(), exports);
		__exportStar(requirePackageUnderstandingFailure(), exports);
		__exportStar(requirePackageDatasheetValidation(), exports);
		__exportStar(requirePackageDatasheetProvenance(), exports);
		__exportStar(requirePackageDatasheetState(), exports);
		__exportStar(requireDatasheetEnsure(), exports);
		__exportStar(requirePackageResolutionDiffView(), exports);
		__exportStar(requirePopcornSigmaModelTypes(), exports);
		exports.ProjectModel = requireProjectModel();
		exports.QueryBuilder = requireQueryBuilder();
		exports.RequirementsV2 = requireRequirementsV2();
		exports.ServiceLocations = requireServiceLocations();
		exports.SignalMapping = requireSignalMapping();
		exports.SignalTies = requireSignalTies();
		exports.ModelText = requireModelText();
		__exportStar(requireCadstromUid(), exports);
		exports.Events = requireEvents();
		__exportStar(requireExpressionExpansion(), exports);
		__exportStar(requireRatingsTable(), exports);
		exports.NetlistValidation = requireNetlistValidation();
		__exportStar(requireDataScience(), exports);
		__exportStar(requireDocumentRetrieval(), exports);
		__exportStar(requireGenerateReadableId(), exports);
		__exportStar(requireIsValidPdf(), exports);
		__exportStar(requirePdfBody(), exports);
		__exportStar(requireCitationPageElements_types(), exports);
		exports.Templating = requireTemplating();
		exports.Utils = requireUtils();
		exports.Wizard = requireWizard();
		__exportStar(requireGeneratorIds(), exports);
		__exportStar(requireOrgDefaults(), exports);
		__exportStar(requirePackageReview(), exports);
		__exportStar(requirePlanPriority(), exports);
		__exportStar(requireReviewTerminalStatus(), exports);
		__exportStar(requireErrors(), exports);
		var validation_1 = requireValidation();
		Object.defineProperty(exports, "validateFile", { enumerable: true, get: function () { return validation_1.validateFile; } });
		var net_drive_result_drive_mode_1 = requireNetDriveResultDriveMode();
		Object.defineProperty(exports, "NetDriveResultDriveMode", { enumerable: true, get: function () { return net_drive_result_drive_mode_1.NetDriveResultDriveMode; } });
		Object.defineProperty(exports, "isNetDriveResultDriveMode", { enumerable: true, get: function () { return net_drive_result_drive_mode_1.isNetDriveResultDriveMode; } });
		Object.defineProperty(exports, "NET_DRIVE_RESULT_DRIVE_MODES", { enumerable: true, get: function () { return net_drive_result_drive_mode_1.NET_DRIVE_RESULT_DRIVE_MODES; } });
		var package_status_1 = requirePackageStatus();
		Object.defineProperty(exports, "PackageStatus", { enumerable: true, get: function () { return package_status_1.PackageStatus; } });
		Object.defineProperty(exports, "packageStatusSchema", { enumerable: true, get: function () { return package_status_1.packageStatusSchema; } });
		var package_operator_review_status_1 = requirePackageOperatorReviewStatus();
		Object.defineProperty(exports, "PackageOperatorReviewStatus", { enumerable: true, get: function () { return package_operator_review_status_1.PackageOperatorReviewStatus; } });
		Object.defineProperty(exports, "packageOperatorReviewStatusSchema", { enumerable: true, get: function () { return package_operator_review_status_1.packageOperatorReviewStatusSchema; } });
		var pinout_validation_result_1 = requirePinoutValidationResult();
		Object.defineProperty(exports, "pinoutValidationResultSchema", { enumerable: true, get: function () { return pinout_validation_result_1.pinoutValidationResultSchema; } });
		var build_status_1 = requireBuildStatus();
		Object.defineProperty(exports, "BuildStatus", { enumerable: true, get: function () { return build_status_1.BuildStatus; } });
		Object.defineProperty(exports, "buildStatusSchema", { enumerable: true, get: function () { return build_status_1.buildStatusSchema; } });
		Object.defineProperty(exports, "BuildTimeoutReason", { enumerable: true, get: function () { return build_status_1.BuildTimeoutReason; } });
		Object.defineProperty(exports, "buildTimeoutReasonSchema", { enumerable: true, get: function () { return build_status_1.buildTimeoutReasonSchema; } });
		Object.defineProperty(exports, "isBuildStatus", { enumerable: true, get: function () { return build_status_1.isBuildStatus; } });
		Object.defineProperty(exports, "BUILD_STATUSES", { enumerable: true, get: function () { return build_status_1.BUILD_STATUSES; } });
		Object.defineProperty(exports, "isActiveProcessingStatus", { enumerable: true, get: function () { return build_status_1.isActiveProcessingStatus; } });
		Object.defineProperty(exports, "isBomReadyOrLaterStatus", { enumerable: true, get: function () { return build_status_1.isBomReadyOrLaterStatus; } });
		Object.defineProperty(exports, "isFinalBuildStatus", { enumerable: true, get: function () { return build_status_1.isFinalBuildStatus; } });
		var build_status_transitions_1 = requireBuildStatusTransitions();
		Object.defineProperty(exports, "BUILD_STATUS_TRANSITIONS", { enumerable: true, get: function () { return build_status_transitions_1.BUILD_STATUS_TRANSITIONS; } });
		Object.defineProperty(exports, "TransitionOutcome", { enumerable: true, get: function () { return build_status_transitions_1.TransitionOutcome; } });
		Object.defineProperty(exports, "canTransition", { enumerable: true, get: function () { return build_status_transitions_1.canTransition; } });
		Object.defineProperty(exports, "legalPredecessorsOf", { enumerable: true, get: function () { return build_status_transitions_1.legalPredecessorsOf; } });
		Object.defineProperty(exports, "isTerminalBuildStatus", { enumerable: true, get: function () { return build_status_transitions_1.isTerminalBuildStatus; } });
		Object.defineProperty(exports, "ANALYZABLE_BUILD_STATUSES", { enumerable: true, get: function () { return build_status_transitions_1.ANALYZABLE_BUILD_STATUSES; } });
		Object.defineProperty(exports, "isAnalyzableBuildStatus", { enumerable: true, get: function () { return build_status_transitions_1.isAnalyzableBuildStatus; } });
		Object.defineProperty(exports, "PRE_REVIEW_BUILD_STATUSES", { enumerable: true, get: function () { return build_status_transitions_1.PRE_REVIEW_BUILD_STATUSES; } });
		Object.defineProperty(exports, "isPreReviewBuildStatus", { enumerable: true, get: function () { return build_status_transitions_1.isPreReviewBuildStatus; } });
		Object.defineProperty(exports, "POST_REVIEW_ERROR_ORIGIN_STATUSES", { enumerable: true, get: function () { return build_status_transitions_1.POST_REVIEW_ERROR_ORIGIN_STATUSES; } });
		Object.defineProperty(exports, "canBeginBomMerge", { enumerable: true, get: function () { return build_status_transitions_1.canBeginBomMerge; } });
		Object.defineProperty(exports, "APPROACHING_ANALYSIS_BUILD_STATUSES", { enumerable: true, get: function () { return build_status_transitions_1.APPROACHING_ANALYSIS_BUILD_STATUSES; } });
		Object.defineProperty(exports, "isBoundForAnalysis", { enumerable: true, get: function () { return build_status_transitions_1.isBoundForAnalysis; } });
		Object.defineProperty(exports, "TIMED_OUT_PREDECESSORS_BY_REASON", { enumerable: true, get: function () { return build_status_transitions_1.TIMED_OUT_PREDECESSORS_BY_REASON; } });
		var build_automation_1 = requireBuildAutomation();
		Object.defineProperty(exports, "AI_ANALYSIS_INCOMPLETE_REASON_PREFIX", { enumerable: true, get: function () { return build_automation_1.AI_ANALYSIS_INCOMPLETE_REASON_PREFIX; } });
		Object.defineProperty(exports, "AI_ANALYSIS_RECOVERY_FINDINGS_RUN_ID_PREFIX", { enumerable: true, get: function () { return build_automation_1.AI_ANALYSIS_RECOVERY_FINDINGS_RUN_ID_PREFIX; } });
		Object.defineProperty(exports, "ANALYSIS_FAILED_STATUS_MESSAGE", { enumerable: true, get: function () { return build_automation_1.ANALYSIS_FAILED_STATUS_MESSAGE; } });
		Object.defineProperty(exports, "ANALYSIS_OFF_RAMP_REASON_PREFIX", { enumerable: true, get: function () { return build_automation_1.ANALYSIS_OFF_RAMP_REASON_PREFIX; } });
		Object.defineProperty(exports, "AnalysisDispatchClaimOutcome", { enumerable: true, get: function () { return build_automation_1.AnalysisDispatchClaimOutcome; } });
		Object.defineProperty(exports, "BuildAutomationMode", { enumerable: true, get: function () { return build_automation_1.BuildAutomationMode; } });
		Object.defineProperty(exports, "BuildAutomationStep", { enumerable: true, get: function () { return build_automation_1.BuildAutomationStep; } });
		Object.defineProperty(exports, "BuildEnrichmentArm", { enumerable: true, get: function () { return build_automation_1.BuildEnrichmentArm; } });
		Object.defineProperty(exports, "BuildStatusActor", { enumerable: true, get: function () { return build_automation_1.BuildStatusActor; } });
		Object.defineProperty(exports, "FAST_MODE_ANALYSIS_RUN_ID_PREFIX", { enumerable: true, get: function () { return build_automation_1.FAST_MODE_ANALYSIS_RUN_ID_PREFIX; } });
		Object.defineProperty(exports, "buildAutomationModeSchema", { enumerable: true, get: function () { return build_automation_1.buildAutomationModeSchema; } });
		Object.defineProperty(exports, "isAutomationStepEnabled", { enumerable: true, get: function () { return build_automation_1.isAutomationStepEnabled; } });
		Object.defineProperty(exports, "isBuildAutomationMode", { enumerable: true, get: function () { return build_automation_1.isBuildAutomationMode; } });
		Object.defineProperty(exports, "isBuildStatusActor", { enumerable: true, get: function () { return build_automation_1.isBuildStatusActor; } });
		var json_value_1 = requireJsonValue();
		Object.defineProperty(exports, "JsonValueSchema", { enumerable: true, get: function () { return json_value_1.JsonValueSchema; } });
		var zod_json_schema_1 = requireZodJsonSchema();
		Object.defineProperty(exports, "ZodJsonSchema", { enumerable: true, get: function () { return zod_json_schema_1.ZodJsonSchema; } });
		Object.defineProperty(exports, "ZodJsonSchemaTarget", { enumerable: true, get: function () { return zod_json_schema_1.ZodJsonSchemaTarget; } });
		var pipeline_ops_1 = requirePipelineOps();
		Object.defineProperty(exports, "AiAnalysisRecoveryBlockerKind", { enumerable: true, get: function () { return pipeline_ops_1.AiAnalysisRecoveryBlockerKind; } });
		Object.defineProperty(exports, "AiAnalysisRecoveryMode", { enumerable: true, get: function () { return pipeline_ops_1.AiAnalysisRecoveryMode; } });
		Object.defineProperty(exports, "NETLIST_WAIT_EXPIRED_WORKFLOW_STAGE", { enumerable: true, get: function () { return pipeline_ops_1.NETLIST_WAIT_EXPIRED_WORKFLOW_STAGE; } });
		Object.defineProperty(exports, "PIPELINE_AWAITING_STATE_BY_ATTRIBUTE", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_AWAITING_STATE_BY_ATTRIBUTE; } });
		Object.defineProperty(exports, "PIPELINE_OPS_DEFAULT_WINDOW_DAYS", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_OPS_DEFAULT_WINDOW_DAYS; } });
		Object.defineProperty(exports, "PIPELINE_OPS_EXCLUSIONS", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_OPS_EXCLUSIONS; } });
		Object.defineProperty(exports, "PIPELINE_OPS_INTERNAL_BILLING_CLASS", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_OPS_INTERNAL_BILLING_CLASS; } });
		Object.defineProperty(exports, "PIPELINE_OPS_MAX_WINDOW_DAYS", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_OPS_MAX_WINDOW_DAYS; } });
		Object.defineProperty(exports, "PIPELINE_STAGES", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_STAGES; } });
		Object.defineProperty(exports, "PIPELINE_STAGE_BY_BUILD_STATUS", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_STAGE_BY_BUILD_STATUS; } });
		Object.defineProperty(exports, "DeliveryHealthReason", { enumerable: true, get: function () { return pipeline_ops_1.DeliveryHealthReason; } });
		Object.defineProperty(exports, "PIPELINE_OPS_UNLISTED_RUN_LIMIT", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_OPS_UNLISTED_RUN_LIMIT; } });
		Object.defineProperty(exports, "PIPELINE_WORKFLOW_CLOSED_STATUSES", { enumerable: true, get: function () { return pipeline_ops_1.PIPELINE_WORKFLOW_CLOSED_STATUSES; } });
		Object.defineProperty(exports, "PipelineAwaitingState", { enumerable: true, get: function () { return pipeline_ops_1.PipelineAwaitingState; } });
		Object.defineProperty(exports, "PipelineNetlistFailure", { enumerable: true, get: function () { return pipeline_ops_1.PipelineNetlistFailure; } });
		Object.defineProperty(exports, "PipelineOpsAction", { enumerable: true, get: function () { return pipeline_ops_1.PipelineOpsAction; } });
		Object.defineProperty(exports, "PipelineOpsExclusion", { enumerable: true, get: function () { return pipeline_ops_1.PipelineOpsExclusion; } });
		Object.defineProperty(exports, "PipelineOpsUnlistedReason", { enumerable: true, get: function () { return pipeline_ops_1.PipelineOpsUnlistedReason; } });
		Object.defineProperty(exports, "PipelineStage", { enumerable: true, get: function () { return pipeline_ops_1.PipelineStage; } });
		Object.defineProperty(exports, "PipelineStageState", { enumerable: true, get: function () { return pipeline_ops_1.PipelineStageState; } });
		Object.defineProperty(exports, "PipelineWorkflowStatus", { enumerable: true, get: function () { return pipeline_ops_1.PipelineWorkflowStatus; } });
		Object.defineProperty(exports, "pipelineAwaitingStateFromAttribute", { enumerable: true, get: function () { return pipeline_ops_1.pipelineAwaitingStateFromAttribute; } });
		Object.defineProperty(exports, "pipelineStageRank", { enumerable: true, get: function () { return pipeline_ops_1.pipelineStageRank; } });
		Object.defineProperty(exports, "pipelineWorkflowStatusFromTemporal", { enumerable: true, get: function () { return pipeline_ops_1.pipelineWorkflowStatusFromTemporal; } });
		Object.defineProperty(exports, "isPipelineOpsExclusion", { enumerable: true, get: function () { return pipeline_ops_1.isPipelineOpsExclusion; } });
		var delivery_health_1 = requireDeliveryHealth();
		Object.defineProperty(exports, "DELIVERY_HEALTH_SEVERITY", { enumerable: true, get: function () { return delivery_health_1.DELIVERY_HEALTH_SEVERITY; } });
		Object.defineProperty(exports, "DeliveryDatasheetCoverageJudgment", { enumerable: true, get: function () { return delivery_health_1.DeliveryDatasheetCoverageJudgment; } });
		Object.defineProperty(exports, "DeliveryFunnelGate", { enumerable: true, get: function () { return delivery_health_1.DeliveryFunnelGate; } });
		Object.defineProperty(exports, "DeliverySlaJudgment", { enumerable: true, get: function () { return delivery_health_1.DeliverySlaJudgment; } });
		Object.defineProperty(exports, "DeliverySlaSource", { enumerable: true, get: function () { return delivery_health_1.DeliverySlaSource; } });
		Object.defineProperty(exports, "deliveryHealthSeverity", { enumerable: true, get: function () { return delivery_health_1.deliveryHealthSeverity; } });
		Object.defineProperty(exports, "isDeliveryDatasheetCoverageJudgment", { enumerable: true, get: function () { return delivery_health_1.isDeliveryDatasheetCoverageJudgment; } });
		Object.defineProperty(exports, "isDeliveryFunnelGate", { enumerable: true, get: function () { return delivery_health_1.isDeliveryFunnelGate; } });
		Object.defineProperty(exports, "isDeliveryHealthReason", { enumerable: true, get: function () { return delivery_health_1.isDeliveryHealthReason; } });
		Object.defineProperty(exports, "isDeliverySlaJudgment", { enumerable: true, get: function () { return delivery_health_1.isDeliverySlaJudgment; } });
		Object.defineProperty(exports, "isDeliverySlaSource", { enumerable: true, get: function () { return delivery_health_1.isDeliverySlaSource; } });
		var domain_events_1 = requireDomainEvents();
		Object.defineProperty(exports, "BuildDomainEventType", { enumerable: true, get: function () { return domain_events_1.BuildDomainEventType; } });
		Object.defineProperty(exports, "DomainEventSubjectType", { enumerable: true, get: function () { return domain_events_1.DomainEventSubjectType; } });
		Object.defineProperty(exports, "PipelinePhase", { enumerable: true, get: function () { return domain_events_1.PipelinePhase; } });
		Object.defineProperty(exports, "ReviewerRoleChangePayloadSchema", { enumerable: true, get: function () { return domain_events_1.ReviewerRoleChangePayloadSchema; } });
		Object.defineProperty(exports, "ReviewerRoleDomainEventType", { enumerable: true, get: function () { return domain_events_1.ReviewerRoleDomainEventType; } });
		Object.defineProperty(exports, "isPipelinePhase", { enumerable: true, get: function () { return domain_events_1.isPipelinePhase; } });
		var netlist_gate_1 = requireNetlistGate();
		Object.defineProperty(exports, "NetlistClearanceOutcome", { enumerable: true, get: function () { return netlist_gate_1.NetlistClearanceOutcome; } });
		Object.defineProperty(exports, "NetlistGateState", { enumerable: true, get: function () { return netlist_gate_1.NetlistGateState; } });
		Object.defineProperty(exports, "NetlistSource", { enumerable: true, get: function () { return netlist_gate_1.NetlistSource; } });
		var netlist_triage_1 = requireNetlistTriage();
		Object.defineProperty(exports, "NetlistTriageLocations", { enumerable: true, get: function () { return netlist_triage_1.NetlistTriageLocations; } });
		Object.defineProperty(exports, "NetlistTriagePointerSchema", { enumerable: true, get: function () { return netlist_triage_1.NetlistTriagePointerSchema; } });
		var remediation_1 = requireRemediation$1();
		Object.defineProperty(exports, "NetlistGenerationFailureCode", { enumerable: true, get: function () { return remediation_1.NetlistGenerationFailureCode; } });
		Object.defineProperty(exports, "NetlistGenerationRemediation", { enumerable: true, get: function () { return remediation_1.NetlistGenerationRemediation; } });
		Object.defineProperty(exports, "ProjectParseFailureCode", { enumerable: true, get: function () { return remediation_1.ProjectParseFailureCode; } });
		Object.defineProperty(exports, "ProjectParseRemediation", { enumerable: true, get: function () { return remediation_1.ProjectParseRemediation; } });
		Object.defineProperty(exports, "RemediationOwner", { enumerable: true, get: function () { return remediation_1.RemediationOwner; } });
		var restart_processing_outcome_1 = requireRestartProcessingOutcome();
		Object.defineProperty(exports, "RestartProcessingOutcome", { enumerable: true, get: function () { return restart_processing_outcome_1.RestartProcessingOutcome; } });
		Object.defineProperty(exports, "restartProcessingOutcomeSchema", { enumerable: true, get: function () { return restart_processing_outcome_1.restartProcessingOutcomeSchema; } });
		var workflow_start_outcome_1 = requireWorkflowStartOutcome();
		Object.defineProperty(exports, "WorkflowStartOutcome", { enumerable: true, get: function () { return workflow_start_outcome_1.WorkflowStartOutcome; } });
		var analysis_gate_dispatch_1 = requireAnalysisGateDispatch();
		Object.defineProperty(exports, "AnalysisGateDispatch", { enumerable: true, get: function () { return analysis_gate_dispatch_1.AnalysisGateDispatch; } });
		Object.defineProperty(exports, "analysisGateDispatchSchema", { enumerable: true, get: function () { return analysis_gate_dispatch_1.analysisGateDispatchSchema; } });
		var workflow_start_status_1 = requireWorkflowStartStatus();
		Object.defineProperty(exports, "WorkflowStartStatus", { enumerable: true, get: function () { return workflow_start_status_1.WorkflowStartStatus; } });
		Object.defineProperty(exports, "workflowStartStatusSchema", { enumerable: true, get: function () { return workflow_start_status_1.workflowStartStatusSchema; } });
		Object.defineProperty(exports, "isWorkflowStartStatus", { enumerable: true, get: function () { return workflow_start_status_1.isWorkflowStartStatus; } });
		var catalogue_datasheet_attach_outcome_1 = requireCatalogueDatasheetAttachOutcome();
		Object.defineProperty(exports, "CatalogueDatasheetAttachOutcome", { enumerable: true, get: function () { return catalogue_datasheet_attach_outcome_1.CatalogueDatasheetAttachOutcome; } });
		Object.defineProperty(exports, "MAX_CATALOGUE_PICK_ROWS", { enumerable: true, get: function () { return catalogue_datasheet_attach_outcome_1.MAX_CATALOGUE_PICK_ROWS; } });
		var revision_1 = requireRevision();
		Object.defineProperty(exports, "revisionWithFilesSchema", { enumerable: true, get: function () { return revision_1.revisionWithFilesSchema; } });
		Object.defineProperty(exports, "revisionSchema", { enumerable: true, get: function () { return revision_1.revisionSchema; } });
		Object.defineProperty(exports, "revisionStatusSchema", { enumerable: true, get: function () { return revision_1.revisionStatusSchema; } });
		Object.defineProperty(exports, "revisionFileSchema", { enumerable: true, get: function () { return revision_1.revisionFileSchema; } });
		var build_1 = requireBuild();
		Object.defineProperty(exports, "buildSchema", { enumerable: true, get: function () { return build_1.buildSchema; } });
		Object.defineProperty(exports, "buildFileSchema", { enumerable: true, get: function () { return build_1.buildFileSchema; } });
		Object.defineProperty(exports, "buildWithFilesSchema", { enumerable: true, get: function () { return build_1.buildWithFilesSchema; } });
		var customer_facing_bom_item_1 = requireCustomerFacingBomItem();
		Object.defineProperty(exports, "customerFacingBomItemStaticMetadataSchema", { enumerable: true, get: function () { return customer_facing_bom_item_1.customerFacingBomItemStaticMetadataSchema; } });
		var hil_module_resolution_type_1 = requireHilModuleResolutionType();
		Object.defineProperty(exports, "HilModuleResolutionType", { enumerable: true, get: function () { return hil_module_resolution_type_1.HilModuleResolutionType; } });
		Object.defineProperty(exports, "hilModuleResolutionTypeSchema", { enumerable: true, get: function () { return hil_module_resolution_type_1.hilModuleResolutionTypeSchema; } });
		var customer_read_only_bom_item_1 = requireCustomerReadOnlyBomItem();
		Object.defineProperty(exports, "customerReadOnlyBomItemSchema", { enumerable: true, get: function () { return customer_read_only_bom_item_1.customerReadOnlyBomItemSchema; } });
		Object.defineProperty(exports, "customerReadOnlyBomItemWithStaticMetadataSchema", { enumerable: true, get: function () { return customer_read_only_bom_item_1.customerReadOnlyBomItemWithStaticMetadataSchema; } });
		var customer_editable_bom_item_1 = requireCustomerEditableBomItem();
		Object.defineProperty(exports, "customerEditableBomItemSchema", { enumerable: true, get: function () { return customer_editable_bom_item_1.customerEditableBomItemSchema; } });
		Object.defineProperty(exports, "customerEditableBomItemWithStaticMetadataSchema", { enumerable: true, get: function () { return customer_editable_bom_item_1.customerEditableBomItemWithStaticMetadataSchema; } });
		Object.defineProperty(exports, "customerEditableBomItemUpdateDtoSchema", { enumerable: true, get: function () { return customer_editable_bom_item_1.customerEditableBomItemUpdateDtoSchema; } });
		var manufacturer_names_1 = requireManufacturerNames();
		Object.defineProperty(exports, "ManufacturerNames", { enumerable: true, get: function () { return manufacturer_names_1.ManufacturerNames; } });
		var customer_bom_review_grouping_1 = requireCustomerBomReviewGrouping();
		Object.defineProperty(exports, "IMPORTANT_BOM_REVIEW_MIN_PINS", { enumerable: true, get: function () { return customer_bom_review_grouping_1.IMPORTANT_BOM_REVIEW_MIN_PINS; } });
		Object.defineProperty(exports, "findBomReviewDatasheetSource", { enumerable: true, get: function () { return customer_bom_review_grouping_1.findBomReviewDatasheetSource; } });
		Object.defineProperty(exports, "groupBomReviewItems", { enumerable: true, get: function () { return customer_bom_review_grouping_1.groupBomReviewItems; } });
		Object.defineProperty(exports, "isBomReviewRowWithoutDatasheet", { enumerable: true, get: function () { return customer_bom_review_grouping_1.isBomReviewRowWithoutDatasheet; } });
		Object.defineProperty(exports, "isImportantBomReviewRow", { enumerable: true, get: function () { return customer_bom_review_grouping_1.isImportantBomReviewRow; } });
		Object.defineProperty(exports, "resolveBomReviewDatasheetFileId", { enumerable: true, get: function () { return customer_bom_review_grouping_1.resolveBomReviewDatasheetFileId; } });
		Object.defineProperty(exports, "resolveBomReviewGroupKey", { enumerable: true, get: function () { return customer_bom_review_grouping_1.resolveBomReviewGroupKey; } });
		var bom_datasheet_coverage_1 = requireBomDatasheetCoverage();
		Object.defineProperty(exports, "DatasheetMissingReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.DatasheetMissingReason; } });
		Object.defineProperty(exports, "DatasheetNotApplicableReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.DatasheetNotApplicableReason; } });
		Object.defineProperty(exports, "PackageDatasheetGapReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.PackageDatasheetGapReason; } });
		Object.defineProperty(exports, "TEST_POINT_SIGMA_MODEL_TYPE", { enumerable: true, get: function () { return bom_datasheet_coverage_1.TEST_POINT_SIGMA_MODEL_TYPE; } });
		Object.defineProperty(exports, "bomDatasheetCoverageSchema", { enumerable: true, get: function () { return bom_datasheet_coverage_1.bomDatasheetCoverageSchema; } });
		Object.defineProperty(exports, "isDatasheetNotApplicable", { enumerable: true, get: function () { return bom_datasheet_coverage_1.isDatasheetNotApplicable; } });
		Object.defineProperty(exports, "packageDatasheetGapRowCountsSchema", { enumerable: true, get: function () { return bom_datasheet_coverage_1.packageDatasheetGapRowCountsSchema; } });
		Object.defineProperty(exports, "resolveBomReviewRowDatasheetMissingReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.resolveBomReviewRowDatasheetMissingReason; } });
		Object.defineProperty(exports, "resolveBomReviewRowDatasheetNotApplicableReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.resolveBomReviewRowDatasheetNotApplicableReason; } });
		Object.defineProperty(exports, "resolveBomReviewRowPackageDatasheetGapReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.resolveBomReviewRowPackageDatasheetGapReason; } });
		Object.defineProperty(exports, "resolveDatasheetNotApplicableReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.resolveDatasheetNotApplicableReason; } });
		Object.defineProperty(exports, "resolvePackageDatasheetGapReason", { enumerable: true, get: function () { return bom_datasheet_coverage_1.resolvePackageDatasheetGapReason; } });
		Object.defineProperty(exports, "summarizeBomDatasheetCoverage", { enumerable: true, get: function () { return bom_datasheet_coverage_1.summarizeBomDatasheetCoverage; } });
		var mechanical_not_a_part_1 = requireMechanicalNotAPart();
		Object.defineProperty(exports, "MechanicalNotAPartRule", { enumerable: true, get: function () { return mechanical_not_a_part_1.MechanicalNotAPartRule; } });
		Object.defineProperty(exports, "isMechanicalNotAPart", { enumerable: true, get: function () { return mechanical_not_a_part_1.isMechanicalNotAPart; } });
		Object.defineProperty(exports, "resolveMechanicalNotAPartRule", { enumerable: true, get: function () { return mechanical_not_a_part_1.resolveMechanicalNotAPartRule; } });
		var bom_row_datasheet_status_1 = requireBomRowDatasheetStatus();
		Object.defineProperty(exports, "BomRowDatasheetStatus", { enumerable: true, get: function () { return bom_row_datasheet_status_1.BomRowDatasheetStatus; } });
		Object.defineProperty(exports, "bomRowDatasheetStatusFieldsSchema", { enumerable: true, get: function () { return bom_row_datasheet_status_1.bomRowDatasheetStatusFieldsSchema; } });
		Object.defineProperty(exports, "resolveBomReviewRowDatasheetStatus", { enumerable: true, get: function () { return bom_row_datasheet_status_1.resolveBomReviewRowDatasheetStatus; } });
		Object.defineProperty(exports, "resolveBomRowDatasheetStatus", { enumerable: true, get: function () { return bom_row_datasheet_status_1.resolveBomRowDatasheetStatus; } });
		var catalogue_item_1 = requireCatalogueItem();
		Object.defineProperty(exports, "CatalogueName", { enumerable: true, get: function () { return catalogue_item_1.CatalogueName; } });
		Object.defineProperty(exports, "CatalogueNameSchema", { enumerable: true, get: function () { return catalogue_item_1.CatalogueNameSchema; } });
		Object.defineProperty(exports, "CatalogueMatchKind", { enumerable: true, get: function () { return catalogue_item_1.CatalogueMatchKind; } });
		Object.defineProperty(exports, "CatalogueMatchKindSchema", { enumerable: true, get: function () { return catalogue_item_1.CatalogueMatchKindSchema; } });
		Object.defineProperty(exports, "CatalogueItemSchema", { enumerable: true, get: function () { return catalogue_item_1.CatalogueItemSchema; } });
		Object.defineProperty(exports, "CatalogueItemMetadataSchema", { enumerable: true, get: function () { return catalogue_item_1.CatalogueItemMetadataSchema; } });
		Object.defineProperty(exports, "CATALOGUE_PRIORITY_ORDER", { enumerable: true, get: function () { return catalogue_item_1.CATALOGUE_PRIORITY_ORDER; } });
		Object.defineProperty(exports, "CATALOGUE_PRIORITY_ORDER_QUOTA_CONSERVATIVE", { enumerable: true, get: function () { return catalogue_item_1.CATALOGUE_PRIORITY_ORDER_QUOTA_CONSERVATIVE; } });
		Object.defineProperty(exports, "getPrimaryCatalogueItem", { enumerable: true, get: function () { return catalogue_item_1.getPrimaryCatalogueItem; } });
		Object.defineProperty(exports, "pickPrimaryCatalogueName", { enumerable: true, get: function () { return catalogue_item_1.pickPrimaryCatalogueName; } });
		var tag_1 = requireTag();
		Object.defineProperty(exports, "TagSchema", { enumerable: true, get: function () { return tag_1.TagSchema; } });
		Object.defineProperty(exports, "TagIdentifierSchema", { enumerable: true, get: function () { return tag_1.TagIdentifierSchema; } });
		var user_management_types_1 = requireUserManagement_types();
		Object.defineProperty(exports, "UserSchema", { enumerable: true, get: function () { return user_management_types_1.UserSchema; } });
		Object.defineProperty(exports, "OrganizationSchema", { enumerable: true, get: function () { return user_management_types_1.OrganizationSchema; } });
		Object.defineProperty(exports, "MembershipSchema", { enumerable: true, get: function () { return user_management_types_1.MembershipSchema; } });
		Object.defineProperty(exports, "MembershipWithGroupSchema", { enumerable: true, get: function () { return user_management_types_1.MembershipWithGroupSchema; } });
		Object.defineProperty(exports, "MembershipWithRelationsSchema", { enumerable: true, get: function () { return user_management_types_1.MembershipWithRelationsSchema; } });
		var invite_state_1 = requireInviteState();
		Object.defineProperty(exports, "isInvitePending", { enumerable: true, get: function () { return invite_state_1.isInvitePending; } });
		var document_acceptance_types_1 = requireDocumentAcceptance_types();
		Object.defineProperty(exports, "DocumentTypeSchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentTypeSchema; } });
		Object.defineProperty(exports, "DocumentVariantIdSchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentVariantIdSchema; } });
		Object.defineProperty(exports, "DocumentVersionIdSchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentVersionIdSchema; } });
		Object.defineProperty(exports, "DocumentVersionEntrySchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentVersionEntrySchema; } });
		Object.defineProperty(exports, "DocumentVariantEntrySchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentVariantEntrySchema; } });
		Object.defineProperty(exports, "DocumentTypeEntrySchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentTypeEntrySchema; } });
		Object.defineProperty(exports, "DocumentRegistrySchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentRegistrySchema; } });
		Object.defineProperty(exports, "OrganizationComplianceStateSchema", { enumerable: true, get: function () { return document_acceptance_types_1.OrganizationComplianceStateSchema; } });
		Object.defineProperty(exports, "DocumentComplianceStatusSchema", { enumerable: true, get: function () { return document_acceptance_types_1.DocumentComplianceStatusSchema; } });
		Object.defineProperty(exports, "OrganizationDocumentStatusSchema", { enumerable: true, get: function () { return document_acceptance_types_1.OrganizationDocumentStatusSchema; } });
		Object.defineProperty(exports, "CheckDocumentComplianceInputSchema", { enumerable: true, get: function () { return document_acceptance_types_1.CheckDocumentComplianceInputSchema; } });
		Object.defineProperty(exports, "CheckDocumentComplianceOrganizationPayloadSchema", { enumerable: true, get: function () { return document_acceptance_types_1.CheckDocumentComplianceOrganizationPayloadSchema; } });
		Object.defineProperty(exports, "CheckDocumentComplianceOutputSchema", { enumerable: true, get: function () { return document_acceptance_types_1.CheckDocumentComplianceOutputSchema; } });
		Object.defineProperty(exports, "RecordAcceptanceInputSchema", { enumerable: true, get: function () { return document_acceptance_types_1.RecordAcceptanceInputSchema; } });
		Object.defineProperty(exports, "RecordAcceptanceOutputSchema", { enumerable: true, get: function () { return document_acceptance_types_1.RecordAcceptanceOutputSchema; } });
		Object.defineProperty(exports, "ComplianceStatusClaimSchema", { enumerable: true, get: function () { return document_acceptance_types_1.ComplianceStatusClaimSchema; } });
		Object.defineProperty(exports, "DOCUMENT_CLOCK_JITTER_TOLERANCE_MS", { enumerable: true, get: function () { return document_acceptance_types_1.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS; } });
		Object.defineProperty(exports, "AUTH0_COMPLIANCE_STATUS_CLAIM", { enumerable: true, get: function () { return document_acceptance_types_1.AUTH0_COMPLIANCE_STATUS_CLAIM; } });
		Object.defineProperty(exports, "buildDocumentVersionId", { enumerable: true, get: function () { return document_acceptance_types_1.buildDocumentVersionId; } });
		Object.defineProperty(exports, "parseDocumentVersionId", { enumerable: true, get: function () { return document_acceptance_types_1.parseDocumentVersionId; } });
		Object.defineProperty(exports, "getCurrentDocumentVersion", { enumerable: true, get: function () { return document_acceptance_types_1.getCurrentDocumentVersion; } });
		Object.defineProperty(exports, "isVersionValidForWrite", { enumerable: true, get: function () { return document_acceptance_types_1.isVersionValidForWrite; } });
		var internal_files_types_1 = requireInternalFiles_types();
		Object.defineProperty(exports, "GetPresignedDownloadUrlInputSchema", { enumerable: true, get: function () { return internal_files_types_1.GetPresignedDownloadUrlInputSchema; } });
		Object.defineProperty(exports, "GetPresignedDownloadUrlOutputSchema", { enumerable: true, get: function () { return internal_files_types_1.GetPresignedDownloadUrlOutputSchema; } });
		Object.defineProperty(exports, "GetPresignedUploadUrlsInputSchema", { enumerable: true, get: function () { return internal_files_types_1.GetPresignedUploadUrlsInputSchema; } });
		Object.defineProperty(exports, "GetPresignedUploadUrlsOutputSchema", { enumerable: true, get: function () { return internal_files_types_1.GetPresignedUploadUrlsOutputSchema; } });
		Object.defineProperty(exports, "PresignedUploadItemSchema", { enumerable: true, get: function () { return internal_files_types_1.PresignedUploadItemSchema; } });
		Object.defineProperty(exports, "PresignedUploadSchema", { enumerable: true, get: function () { return internal_files_types_1.PresignedUploadSchema; } });
		Object.defineProperty(exports, "PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS", { enumerable: true, get: function () { return internal_files_types_1.PRESIGNED_DOWNLOAD_URL_MAX_EXPIRY_SECONDS; } });
		Object.defineProperty(exports, "PRESIGNED_DOWNLOAD_URL_DEFAULT_EXPIRY_SECONDS", { enumerable: true, get: function () { return internal_files_types_1.PRESIGNED_DOWNLOAD_URL_DEFAULT_EXPIRY_SECONDS; } });
		Object.defineProperty(exports, "PRESIGNED_UPLOAD_URLS_MAX_ITEMS", { enumerable: true, get: function () { return internal_files_types_1.PRESIGNED_UPLOAD_URLS_MAX_ITEMS; } });
		var ExpressionComparator_1 = requireExpressionComparator();
		Object.defineProperty(exports, "ExpressionComparator", { enumerable: true, get: function () { return ExpressionComparator_1.ExpressionComparator; } });
		__exportStar(requireUrls(), exports);
		
	} (dist$2));
	return dist$2;
}

var distExports$1 = requireDist();

const RASTER_TEXT_METRIC_FONT_SIZE = 100;
/**
 * Resolves the net names a drawing's net info refers to, deduplicated.
 *
 * @param netInfo - A bare net name, or an object carrying a net name and/or per-instance net pointers.
 * @param selectedInstance - When given, restricts the pointer list to the matching instance.
 * @returns The unique net names referenced.
 */
function resolveNetPropertyValues(netInfo = {}, selectedInstance) {
    const { net, nets } = typeof netInfo === 'string' ? { net: netInfo, nets: undefined } : (netInfo ?? {});
    const propNets = new Set();
    if (net) {
        propNets.add(net);
    }
    if (selectedInstance && nets?.length) {
        const match = nets.find((n) => n.i === selectedInstance);
        if (match) {
            propNets.add(match.n);
        }
    }
    else if (nets?.length) {
        for (const netPointer of nets) {
            propNets.add(netPointer.n);
        }
    }
    return [...propNets];
}
function getTextPadding(text) {
    return text.padding ?
        [distExports$1.Parse.Constants.WORLD_SCALE * text.padding[0], distExports$1.Parse.Constants.WORLD_SCALE * text.padding[1]]
        : [0, 0];
}
function getTextWordWrapWidth(text) {
    if (!text.bounds) {
        return undefined;
    }
    const padding = getTextPadding(text);
    return text.bounds[0] * distExports$1.Parse.Constants.WORLD_SCALE - 2 * padding[0];
}
function getRasterTextWordWrapWidth(text, textScale) {
    const wordWrapWidth = getTextWordWrapWidth(text);
    return wordWrapWidth === undefined ? undefined : wordWrapWidth * textScale;
}
/**
 * Budgets for the backing canvas of one rasterized text element, in device
 * pixels. Chosen to stay below the smallest canvas size any major browser
 * will allocate.
 */
const MAX_RASTER_TEXT_CANVAS_DIMENSION = 16384;
const MAX_RASTER_TEXT_CANVAS_AREA = 64_000_000;
/**
 * How much to scale a text rasterization down so its canvas stays within
 * the size budget. Returns 1 when it already fits, otherwise a factor in
 * (0, 1) to multiply the font size and wrap width by.
 * @param width - proposed canvas width, in CSS px
 * @param height - proposed canvas height, in CSS px
 * @param resolution - device-pixel multiplier the canvas will be allocated at
 * @returns the scale factor to apply, at most 1
 */
function getRasterTextCanvasClampFactor(width, height, resolution) {
    const deviceWidth = width * resolution;
    const deviceHeight = height * resolution;
    const dimensionFactor = MAX_RASTER_TEXT_CANVAS_DIMENSION / max(deviceWidth, deviceHeight);
    const areaFactor = Math.sqrt(MAX_RASTER_TEXT_CANVAS_AREA / (deviceWidth * deviceHeight));
    return min(1, min(dimensionFactor, areaFactor));
}
function getRasterTextFontSizeForGlyphHeight(textSize, textScale, glyphHeight, rasterSizeCorrection = 1) {
    return (textScale *
        distExports$1.Parse.Constants.WORLD_SCALE *
        max(textSize, distExports$1.Parse.Constants.MIN_TEXT_SIZE) *
        rasterSizeCorrection *
        (RASTER_TEXT_METRIC_FONT_SIZE / glyphHeight));
}
/**
 * Expands a given rectangle by the given buffer
 * @param rect
 * @param xBuffer
 * @param yBuffer
 * @returns
 */
function expandRect(rect, xBuffer, yBuffer = xBuffer, applyWorldScaling = true) {
    if (applyWorldScaling) {
        xBuffer = xBuffer * distExports$1.Parse.Constants.WORLD_SCALE;
        yBuffer = yBuffer * distExports$1.Parse.Constants.WORLD_SCALE;
    }
    return {
        x1: rect.x1 - xBuffer,
        x2: rect.x2 + xBuffer,
        y1: rect.y1 - yBuffer,
        y2: rect.y2 + yBuffer,
    };
}
function getBoundingRect(elements) {
    if (elements.length === 0) {
        throw new Error('Must have at least one element to calculate bounding rect');
    }
    const rect = {
        x1: Number.POSITIVE_INFINITY,
        y1: Number.POSITIVE_INFINITY,
        x2: Number.NEGATIVE_INFINITY,
        y2: Number.NEGATIVE_INFINITY,
    };
    for (const e of elements) {
        if (e.left < rect.x1) {
            rect.x1 = e.left;
        }
        if (e.right > rect.x2) {
            rect.x2 = e.right;
        }
        if (e.bottom < rect.y1) {
            rect.y1 = e.bottom;
        }
        if (e.top > rect.y2) {
            rect.y2 = e.top;
        }
    }
    return rect;
}
/**
 * Constrains `rect` to fit within `boundingRect`. Does not preserve aspect ratio.
 * @param rect
 * @param boundingRect
 */
function constrainRect(rect, boundingRect) {
    rect = normalizeRect(rect);
    boundingRect = normalizeRect(boundingRect);
    return {
        x1: constrainValue(boundingRect.x1, rect.x1, boundingRect.x2),
        x2: constrainValue(boundingRect.x1, rect.x2, boundingRect.x2),
        y1: constrainValue(boundingRect.y1, rect.y1, boundingRect.y2),
        y2: constrainValue(boundingRect.y1, rect.y2, boundingRect.y2),
    };
}
function normalizeRect(rect) {
    return {
        x1: min(rect.x1, rect.x2),
        x2: max(rect.x1, rect.x2),
        y1: min(rect.y1, rect.y2),
        y2: max(rect.y1, rect.y2),
    };
}
function constrainValue(min, value, max) {
    if (min > max) {
        const tmp = min;
        min = max;
        max = tmp;
    }
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}
function max(a, b) {
    if (typeof a !== 'number') {
        if (typeof b === 'number') {
            return b;
        }
        return Number.NaN;
    }
    if (typeof b !== 'number') {
        return a;
    }
    if (a > b) {
        return a;
    }
    return b;
}
function maxA(...a) {
    if (a.length === 0) {
        return Number.NaN;
    }
    let max = a[0];
    for (let i = 1; i < a.length; ++i) {
        if (a[i] > max) {
            max = a[i];
        }
    }
    return max;
}
function min(a, b) {
    if (typeof a !== 'number') {
        if (typeof b === 'number') {
            return b;
        }
        return Number.NaN;
    }
    if (typeof b !== 'number') {
        return a;
    }
    if (a < b) {
        return a;
    }
    return b;
}
function constrainPositive(num) {
    if (typeof num !== 'number') {
        return 0;
    }
    if (num < 0) {
        return 0;
    }
    return num;
}
function assertInteger(maybeNum) {
    if (typeof maybeNum === 'string') {
        return parseInt(maybeNum);
    }
    return maybeNum;
}
function booleanXor(a, b) {
    return (a || b) && !(a && b);
}
function combineMirrors(a, b) {
    return [booleanXor(Boolean(a && a[0]), Boolean(b && b[0])), booleanXor(Boolean(a && a[1]), Boolean(b && b[1]))];
}
function distanceBetweenPoints(a, b) {
    return Math.sqrt(Math.pow(b[0] - a[0], 2) + Math.pow(b[1] - a[1], 2));
}
function distanceBetweenIPoints(a, b) {
    return Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
}

var ParseExports = requireParse();

/**
 * @returns The same drawings you put in, but with a `layers` property if they didn't have one already.
 **/
function graphicsOverlayDrawing(drawings, defaultLayer) {
    if (!drawings) {
        return [];
    }
    return drawings.map((drawing) => Array.isArray(drawing.layers) ?
        drawing
        : { drawing: drawing, layers: [defaultLayer] });
}

/** Seems to just contain a method for calculating the bounding box of an overlay. */
class OverlayInternal {
    overlay;
    elements;
    disableCacheBoundingBoxes;
    boundingBox;
    constructor(overlay, elements, disableCacheBoundingBoxes) {
        this.overlay = overlay;
        this.elements = elements;
        this.disableCacheBoundingBoxes = disableCacheBoundingBoxes;
    }
    getBoundingBox() {
        if (this.boundingBox) {
            if (!this.disableCacheBoundingBoxes) {
                return this.boundingBox;
            }
            else {
                delete this.boundingBox;
            }
        }
        const area = new ParseExports.Area();
        for (const drawing of graphicsOverlayDrawing(this.overlay.drawing, 0)) {
            area.extendWithDrawing(undefined, drawing.drawing, []);
        }
        const bbox = {
            x1: area.xMin * ConstantsExports.WORLD_SCALE,
            y1: area.yMin * ConstantsExports.WORLD_SCALE,
            x2: area.xMax * ConstantsExports.WORLD_SCALE,
            y2: area.yMax * ConstantsExports.WORLD_SCALE,
        };
        if (!this.disableCacheBoundingBoxes) {
            this.boundingBox = bbox;
        }
        return bbox;
    }
}

class SelectionPoint {
    point;
    constructor(point) {
        this.point = point;
    }
    screenCoords() {
        return {
            x: this.point.screen.x,
            y: this.point.screen.y,
        };
    }
    drawingCoords() {
        return {
            x: this.point.world.x / ConstantsExports.WORLD_SCALE,
            y: -this.point.world.y / ConstantsExports.WORLD_SCALE,
        };
    }
    rendererCoords() {
        return {
            x: this.point.global.x,
            y: this.point.global.y,
        };
    }
}

var lodashExports = requireLodash();
var _ = /*@__PURE__*/getDefaultExportFromCjs(lodashExports);

var GridContainer2 = "#version 300 es\nprecision mediump float;\n\nin vec2 aVertexPosition;\nout vec2 vTextureCoord;\nout vec4 vPositionCoord;\n\nuniform mat3 translationMatrix;\nuniform mat3 projectionMatrix;\n\n// GLSL Vertex Shader for Grid\n// Renders vertices for a simple geometry and passes\n// world-coordinates to the fragment shader in vTextureCoord.\nvoid main() {\n\tvPositionCoord = vec4(\n\t\t(\n\t\t\tprojectionMatrix\n\t\t\t* translationMatrix\n\t\t\t* vec3(aVertexPosition, 1.0)\n\t\t).xy,\n\t\t1.0,\n\t\t1.0\n\t);\n\tgl_Position = vPositionCoord;\n\t\n\tvTextureCoord = (translationMatrix * vec3(aVertexPosition, 1.0)).xy;\n}";

var GridShader2 = "#version 300 es\nprecision highp float;\n\nin vec2 vTextureCoord;\nin vec4 vPositionCoord;\nout mediump vec4 fragColor;\n\n// Screen-space offset of the world origin, in CSS pixels.\nuniform vec2 offset;\n// Distance between minor grid points, in CSS pixels.\nuniform float minorSpacing;\n// Every Nth minor step is a major one; 1 disables majors.\nuniform float majorEvery;\n// 0 = lines, 1 = dots, 2 = dashes, 3 = crosses.\nuniform float gridStyle;\n// Line width and dot diameter, in CSS pixels.\nuniform float lineWidth;\nuniform float dotSize;\n// Dash period along each line, in CSS pixels; a whole fraction of the minor spacing.\nuniform float dashPeriod;\nuniform vec4 minorColor;\nuniform vec4 majorColor;\n// Opacity of the minor grid, faded out as it gets dense.\nuniform float minorFade;\nuniform float opacity;\n\n// Per-axis distance, in pixels, from p to the nearest multiple of spacing.\nvec2 distanceToGrid(vec2 p, float spacing) {\n\tvec2 m = mod(p, spacing);\n\treturn min(m, spacing - m);\n}\n\n// Anti-aliased coverage of a band of the given half width.\nfloat band(float d, float halfWidth, float aa) {\n\treturn 1.0 - smoothstep(halfWidth - 0.5 * aa, halfWidth + 0.5 * aa, d);\n}\n\nfloat coverage(vec2 p, float spacing, vec2 aa, float sizeScale) {\n\tvec2 d = distanceToGrid(p, spacing);\n\tvec2 halfWidth = 0.5 * max(vec2(lineWidth), aa);\n\n\tif (gridStyle < 0.5) {\n\t\treturn max(band(d.x, halfWidth.x, aa.x), band(d.y, halfWidth.y, aa.y));\n\t}\n\n\tif (gridStyle < 1.5) {\n\t\tfloat radius = max(0.5 * dotSize * sizeScale, 0.5 * aa.x);\n\t\treturn band(length(d), radius, aa.x);\n\t}\n\n\tif (gridStyle < 2.5) {\n\t\t// Dashes are centred on the grid points so every intersection is drawn.\n\t\tvec2 along = mod(p + 0.25 * dashPeriod, dashPeriod);\n\t\tfloat onVertical = step(along.y, 0.5 * dashPeriod);\n\t\tfloat onHorizontal = step(along.x, 0.5 * dashPeriod);\n\t\treturn max(band(d.x, halfWidth.x, aa.x) * onVertical, band(d.y, halfWidth.y, aa.y) * onHorizontal);\n\t}\n\n\tfloat arm = dotSize * sizeScale;\n\treturn max(band(d.x, halfWidth.x, aa.x) * step(d.y, arm), band(d.y, halfWidth.y, aa.y) * step(d.x, arm));\n}\n\nvoid main() {\n\tvec2 p = vTextureCoord + offset;\n\tvec2 aa = max(fwidth(p), vec2(1e-4));\n\n\tfloat minor = coverage(p, minorSpacing, aa, 1.0) * minorFade;\n\tfloat major = majorEvery > 1.5 ? coverage(p, minorSpacing * majorEvery, aa, 2.0) : 0.0;\n\n\tvec4 minorPremultiplied = vec4(minorColor.rgb * minorColor.a, minorColor.a) * minor;\n\tvec4 majorPremultiplied = vec4(majorColor.rgb * majorColor.a, majorColor.a) * major;\n\tfragColor = (majorPremultiplied + minorPremultiplied * (1.0 - majorPremultiplied.a)) * opacity;\n}\n";

class BackgroundGrid {
    viewport;
    color;
    majorColor;
    static ToolId = 'grid';
    static MajorToolOpt = 'major';
    static DefaultLineSize = 1;
    static DefaultDotSize = 1;
    static DefaultMajorEvery = 10;
    /** Below this spacing, in CSS pixels, a grid level is too dense to read and is hidden. */
    static MinReadableSpacing = 8;
    static StyleIndex = { line: 0, dot: 1, dash: 2, plus: 3 };
    shader;
    mesh;
    geometry;
    _grid;
    isDestroyed;
    /** Viewport transform the view uniforms were last computed for. */
    lastTransform;
    _gridSupported;
    constructor(viewport, color, majorColor = color) {
        this.viewport = viewport;
        this.color = color;
        this.majorColor = majorColor;
    }
    /**
     * Picks the on-screen spacing and minor-grid fade for a grid step. Minor points fade out as they
     * approach the readable minimum, and once they have faded out the grid coarsens by the major
     * interval, so the old majors become the new minors and the visible spacing never jumps.
     *
     * @param stepPx - On-screen length of one grid step, in CSS pixels.
     * @param majorEvery - Major interval; 1 when the grid has no majors.
     * @returns The minor spacing to draw and how opaque the minor grid is.
     */
    static computeSpacing(stepPx, majorEvery) {
        const min = BackgroundGrid.MinReadableSpacing;
        if (!Number.isFinite(stepPx) || stepPx <= 0) {
            return { minorSpacing: min, minorFade: 0 };
        }
        const hasMajors = majorEvery > 1;
        const coarsenBy = hasMajors ? majorEvery : 5;
        let minorSpacing = stepPx;
        while (minorSpacing < min) {
            minorSpacing *= coarsenBy;
        }
        if (!hasMajors) {
            return { minorSpacing, minorFade: 1 };
        }
        return { minorSpacing, minorFade: constrainValue(0, (minorSpacing - min) / min, 1) };
    }
    /**
     * Snaps a dash period to a whole fraction of the grid spacing, so a dash is centred on every grid
     * point at any spacing.
     *
     * @param minorSpacing - Distance between grid points, in CSS pixels.
     * @param spacingPercent - Requested dash period as a fraction of the spacing.
     * @returns The dash period in CSS pixels.
     */
    static computeDashPeriod(minorSpacing, spacingPercent) {
        const dashesPerStep = Math.max(1, Math.round(1 / constrainValue(0.01, spacingPercent, 1)));
        return minorSpacing / dashesPerStep;
    }
    /**
     * Converts render-state colour options to a shader RGBA vector.
     *
     * @param color - The colour options; the neutral state's tint and alpha are used.
     * @returns Red, green, blue and alpha, each from 0 to 1.
     */
    static toShaderColor(color) {
        const { tint, alpha } = color.selectionNeutral;
        return [((tint >> 16) & 0xff) / 0xff, ((tint >> 8) & 0xff) / 0xff, (tint & 0xff) / 0xff, alpha];
    }
    get grid() {
        return this._grid;
    }
    get unitSize() {
        return Number.isFinite(this._grid?.unitSize) ? (this._grid?.unitSize ?? 0.1) : 0.1;
    }
    get multiple() {
        const multiple = this._grid?.multiple;
        return multiple !== undefined && Number.isFinite(multiple) && multiple >= 1 ? multiple : 1;
    }
    get gridSupported() {
        return this._gridSupported;
    }
    prepareShader(renderer) {
        try {
            const uniforms = this.getUniforms();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const gl = renderer.gl;
            if (gl instanceof WebGL2RenderingContext) {
                this.shader = PIXI.Shader.from(GridContainer2, GridShader2, uniforms);
            }
            else if (gl instanceof WebGLRenderingContext) {
                // TODO WebGL 1 support maybe?
                // this.shader = PIXI.Shader.from(GridContainer1, GridShader1, uniforms);
            }
            this._gridSupported = Boolean(this.shader);
        }
        catch (e) {
            console.warn(`Error loading grid shader: `, e);
            this._gridSupported = false;
        }
        return this.gridSupported;
    }
    render(rect, grid) {
        if (this.isDestroyed) {
            throw new Error('Grid renderer already destroyed');
        }
        if (!this.shader) {
            throw new Error('Must call prepareShader first');
        }
        this._grid = grid;
        for (const [key, value] of Object.entries(this.getUniforms())) {
            this.shader.uniforms[key] = value;
        }
        if (!this.mesh) {
            this.geometry = new PIXI.Geometry()
                // Create a square geometry that renders into the rect
                .addAttribute('aVertexPosition', [rect.x1, rect.y1, rect.x2, rect.y1, rect.x2, rect.y2, rect.x1, rect.y2], 2)
                .addIndex([0, 1, 2, 0, 2, 3]);
            this.mesh = new PIXI.Mesh(this.geometry, this.shader);
            // Programmatic zooms and pans emit no viewport events, so follow the transform every frame.
            this.viewport.on('frame-end', this.handleFrameEnd);
            this.color.changeWatcher?.on('changed', this.updateGridColor);
        }
        return this.mesh;
    }
    destroy() {
        this.isDestroyed = true;
        if (this.mesh) {
            this.viewport.removeListener('frame-end', this.handleFrameEnd);
            this.color.changeWatcher?.removeListener('changed', this.updateGridColor);
            this.mesh.destroy();
            this.geometry.destroy();
        }
        if (this.shader) {
            this.shader.destroy();
        }
    }
    /**
     * Assembles every shader uniform for the current grid settings and view.
     *
     * @returns The view uniforms plus the grid's style, sizes, dash period, colours and opacity.
     */
    getUniforms() {
        const style = this._grid?.style ?? 'line';
        const view = this.getViewUniforms();
        return {
            ...view,
            majorEvery: Math.max(1, Math.round(this._grid?.majorEvery ?? BackgroundGrid.DefaultMajorEvery)),
            gridStyle: BackgroundGrid.StyleIndex[style],
            lineWidth: this._grid?.lineSize ?? BackgroundGrid.DefaultLineSize,
            dotSize: this._grid?.dotSize ?? BackgroundGrid.DefaultDotSize,
            dashPeriod: BackgroundGrid.computeDashPeriod(view.minorSpacing, this._grid?.spacingPercent ?? 0.25),
            minorColor: BackgroundGrid.toShaderColor(this.color),
            majorColor: BackgroundGrid.toShaderColor(this.majorColor),
            opacity: constrainValue(0, this._grid?.opacity ?? 1, 1),
        };
    }
    /**
     * Computes the uniforms that change as the viewport zooms and pans.
     *
     * @returns The origin offset, minor spacing and minor fade for the current view.
     */
    getViewUniforms() {
        const offsetCoords = this.viewport.toScreen(0, 0);
        const stepPx = this.getScreenLength(this.unitSize * this.multiple, offsetCoords);
        const majorEvery = Math.max(1, Math.round(this._grid?.majorEvery ?? BackgroundGrid.DefaultMajorEvery));
        return {
            offset: [-offsetCoords.x, -offsetCoords.y],
            ...BackgroundGrid.computeSpacing(stepPx, majorEvery),
        };
    }
    getScreenLength(value, offsetCoords) {
        if (!offsetCoords) {
            offsetCoords = this.viewport.toScreen(0, 0);
        }
        return this.viewport.toScreen(value * ConstantsExports.WORLD_SCALE, 0).x - offsetCoords.x;
    }
    /** Updates the view uniforms after any frame in which the viewport's position or zoom changed. */
    handleFrameEnd = () => {
        const { x, y } = this.viewport;
        const scale = this.viewport.scale.x;
        const last = this.lastTransform;
        if (last?.x === x && last.y === y && last.scale === scale) {
            return;
        }
        this.lastTransform = { x, y, scale };
        this.updateUniforms();
    };
    updateUniforms = () => {
        if (this.isDestroyed || !this.shader) {
            return;
        }
        const view = this.getViewUniforms();
        this.shader.uniforms['offset'] = view.offset;
        this.shader.uniforms['minorSpacing'] = view.minorSpacing;
        this.shader.uniforms['minorFade'] = view.minorFade;
        this.shader.uniforms['dashPeriod'] = BackgroundGrid.computeDashPeriod(view.minorSpacing, this._grid?.spacingPercent ?? 0.25);
    };
    updateGridColor = () => {
        if (this.isDestroyed || !this.shader) {
            return;
        }
        this.shader.uniforms['minorColor'] = BackgroundGrid.toShaderColor(this.color);
        this.shader.uniforms['majorColor'] = BackgroundGrid.toShaderColor(this.majorColor);
    };
}

class TextureManager {
    entries = new Map();
    loadQueue = [];
    activeLoads = 0;
    maxConcurrentLoads = 4;
    destroyed = false;
    acquire(url, onLoaded, onError) {
        if (this.destroyed) {
            return;
        }
        const existing = this.entries.get(url);
        if (existing) {
            if (existing.state === 'loaded' && existing.texture) {
                onLoaded(existing.texture);
                return;
            }
            if (existing.state === 'error') {
                onError?.(new Error(`Failed to load texture: ${url}`));
                return;
            }
            // Still loading or pending — queue the listener
            existing.listeners.push({ onLoaded, onError });
            return;
        }
        // New entry
        const entry = {
            url,
            state: 'pending',
            texture: null,
            listeners: [{ onLoaded, onError }],
        };
        this.entries.set(url, entry);
        this.loadQueue.push(url);
        this.processQueue();
    }
    destroy() {
        this.destroyed = true;
        this.loadQueue.length = 0;
        this.entries.clear();
        this.activeLoads = 0;
    }
    processQueue() {
        while (this.activeLoads < this.maxConcurrentLoads && this.loadQueue.length > 0) {
            const url = this.loadQueue.shift();
            const entry = this.entries.get(url);
            if (!entry || entry.state !== 'pending') {
                continue;
            }
            entry.state = 'loading';
            this.activeLoads++;
            this.loadTexture(url);
        }
    }
    loadTexture(url) {
        const texture = PIXI.Texture.from(url);
        if (texture.baseTexture.valid) {
            this.onTextureLoaded(url, texture);
            return;
        }
        texture.baseTexture.once('loaded', () => {
            this.onTextureLoaded(url, texture);
        });
        texture.baseTexture.once('error', () => {
            this.onTextureError(url, new Error(`Failed to load texture: ${url}`));
        });
    }
    onTextureLoaded(url, texture) {
        if (this.destroyed) {
            return;
        }
        const entry = this.entries.get(url);
        if (!entry) {
            return;
        }
        entry.state = 'loaded';
        entry.texture = texture;
        this.activeLoads--;
        for (const listener of entry.listeners) {
            listener.onLoaded(texture);
        }
        entry.listeners.length = 0;
        this.processQueue();
    }
    onTextureError(url, error) {
        if (this.destroyed) {
            return;
        }
        const entry = this.entries.get(url);
        if (!entry) {
            return;
        }
        entry.state = 'error';
        this.activeLoads--;
        for (const listener of entry.listeners) {
            listener.onError?.(error);
        }
        entry.listeners.length = 0;
        this.processQueue();
    }
}

class DrawingManager extends EventEmitter {
    renderer;
    colorProvider;
    interactionState;
    opts;
    // There are four layers in addition to those given from the project model. These layers have very high z indexes to
    // ensure they are always on top of layers from the design.
    static SelectedElementsLayer = ConstantsExports.SelectedElementsLayer;
    static OverlayLayer = ConstantsExports.OverlayLayer;
    static UserSelectionLayer = ConstantsExports.UserSelectionLayer;
    static BackgroundLayer = ConstantsExports.BackgroundLayer;
    static DefaultMouseMoveDebounce = 150;
    static DragPixelThreshold = 10;
    /** Maps layer IDs to PixiJS layers */
    layers = new Map();
    /**
     * Maps layer IDs to PixiJS groups (it's one group per layer, so the layer ID describes both the layer and the group)
     **/
    layerGroups = new Map();
    mappedDrawable = {};
    _elements = [];
    tags = new Map();
    userSelectionGroup;
    _selectionMode = SelectionMode.None;
    overlays;
    drawOverlayFunc;
    selectedElements;
    dragging = false;
    disableViewportDragWhileDragging = false;
    selectedRectangle;
    elementsSelectionTypes = new Map();
    customOverlayLayers;
    defaultVisibleLayers;
    visibleLayers;
    _isDestroyed;
    isMouseOver = false;
    background;
    grid;
    _gridSettings;
    tagVisibilityState = new Map();
    _netColorProvider;
    interactiveElements = new Map();
    app;
    viewport;
    warnings = [];
    elementSelectionGroup;
    textureManager = new TextureManager();
    enableBoundsChecksOnRender = false;
    linkHandlingMode = LinkHandlingMode.Disabled;
    constructor(renderer, colorProvider, interactionState, layers, opts = {}) {
        super();
        this.renderer = renderer;
        this.colorProvider = colorProvider;
        this.interactionState = interactionState;
        this.opts = opts;
        // Initialize the PixiJS application
        this.app = renderer.getApp();
        this.viewport = renderer.getViewport();
        if (!this.app.stage) {
            this.app.stage = new Stage();
        }
        // Configure pixi-viewport
        this.renderer.setDragOpts({
            mouseButtons: 'middle-right',
        });
        // Listen for some events coming from `InteractionState`
        if (this.interactionState) {
            this.interactionState.on('clearSelection', this._clearSelection);
            this.interactionState.on('selectElementsByProperty', this._selectElementsByProperty);
            this.interactionState.on('clearHighlight', this._clearHighlight);
            this.interactionState.on('highlightElementsByProperty', this.highlightElementsByProperty);
        }
        // === Set up the scene graph ===
        this.app.stage.sortableChildren = true;
        // There are some special layers which always appear over the regular design layers
        this.elementSelectionGroup = new Group(DrawingManager.SelectedElementsLayer, true);
        this.app.stage.addChild(new Layer$1(this.elementSelectionGroup));
        this.userSelectionGroup = new Group(DrawingManager.UserSelectionLayer);
        this.app.stage.addChild(new Layer$1(this.userSelectionGroup));
        this.createLayerGroup(DrawingManager.OverlayLayer);
        this.createLayerGroup(DrawingManager.BackgroundLayer);
        // The background layer is a container for the background grid and always appears at the back
        this.background = new PIXI.Container();
        this.background.interactive = false;
        this.background.parentGroup = this.layerGroups.get(DrawingManager.BackgroundLayer);
        this.background.zIndex = DrawingManager.BackgroundLayer;
        this.viewport.addChild(this.background);
        // Get the design layers into PixiJS layers
        const maxLayer = Math.max(...layers.map((l) => l.id)) + 1;
        const defaultVisibleLayers = [];
        for (const layer of layers) {
            // I'm not yet sure whether a `Group` is strictly necessary every time we make a layer
            const layerGroup = new Group(this.getLayerOrderForLayer(layer, maxLayer), false);
            const pixiLayer = new Layer$1(layerGroup);
            this.app.stage.addChild(pixiLayer);
            this.layers.set(layer.id, layer);
            this.layerGroups.set(layer.id, layerGroup);
            if (layer.defaultVisible) {
                defaultVisibleLayers.push(layer.id);
            }
        }
        this.setDefaultVisibleLayers(defaultVisibleLayers);
        this.setVisibleLayers(defaultVisibleLayers); // Note that this is actually redundant since `setDefaultVisibleLayers` already does this
        this.app.stage.interactive = true;
        if (this.opts.mouseDebounceOpts === false) {
            this.mouseEventEmitter = (event) => {
                this.emit('mouseMove', new SelectionPoint(event));
            };
        }
        else {
            this.mouseEventEmitter = _.debounce((event) => {
                this.emit('mouseMove', new SelectionPoint(event));
            }, 0, this.opts.mouseDebounceOpts ?? {
                trailing: true,
                leading: true,
                maxWait: DrawingManager.DefaultMouseMoveDebounce,
            });
        }
        this.selectedRectangle = new SelectionRectangle(this, this.colorProvider.getColorForLayer(DrawingManager.OverlayLayer));
        this.addInteractiveElement(this.selectedRectangle);
        this.app.stage
            .on('pointermove', this.onPointerMove)
            .on('pointerover', this.onPointerOver)
            .on('pointerout', this.onPointerOut)
            .on('pointerdown', this.onPointerDown)
            .on('pointerup', this.onPointerUp)
            .on('pointerupoutside', this.onPointerUp);
    }
    get selectionMode() {
        return this._selectionMode;
    }
    get isDestroyed() {
        return Boolean(this._isDestroyed);
    }
    get isGridRendered() {
        return this.background.children.length > 0;
    }
    get isGridSupported() {
        this.prepareGrid();
        return this.grid.gridSupported;
    }
    get isDragging() {
        return this.dragging;
    }
    get currentGrid() {
        return (this._gridSettings || {
            distance: 0.1,
            multiple: 1,
            units: 'mm',
            unitSize: 0.1,
        });
    }
    set currentGrid(value) {
        if (value !== this._gridSettings) {
            this._gridSettings = value;
            if (this.isGridRendered) {
                this.renderGrid(this._gridSettings);
            }
        }
    }
    get netColorProvider() {
        return this._netColorProvider;
    }
    get elements() {
        return this._elements;
    }
    setSelectionMode(mode) {
        if (mode === this.selectionMode) {
            return;
        }
        // Don't update the selection until a new selection
        // is made or manually calling clear selection
        this.selectionModeChanged(mode);
    }
    setColorizationMode(mode, colorProvider) {
        if (mode !== DrawingColorizationMode.Layer && !colorProvider) {
            // Question: why does the 'Layer' mode not require a color provider?
            throw new Error('ColorProvider required for modes other than Layer');
        }
        this._netColorProvider = colorProvider;
        for (const element of this._elements) {
            element.setColorizationMode(mode);
        }
    }
    setVisibleObjectTypes(types) {
        for (const element of this._elements) {
            element.setVisibleObjectTypes(types);
        }
    }
    getObjectSelectionTypeForMode(mode) {
        switch (mode) {
            case SelectionMode.SingleNet:
                return ObjectSelectionType.Net;
            case SelectionMode.SingleObject:
            case SelectionMode.MultipleObject:
                return ObjectSelectionType.Object;
            case SelectionMode.AnySingleNetOrObject:
                return ObjectSelectionType.Any;
        }
        return ObjectSelectionType.None;
    }
    *iterateProp(prop) {
        if (!prop) {
            return;
        }
        yield* prop.values();
    }
    iterateModules() {
        return this.iterateProp(this.mappedDrawable.modules);
    }
    iterateTracks() {
        return this.iterateProp(this.mappedDrawable.tracks);
    }
    iterateZones() {
        return this.iterateProp(this.mappedDrawable.zones);
    }
    iterateDrawings() {
        return this.iterateProp(this.mappedDrawable.drawings);
    }
    iterateEdges() {
        return this.iterateProp(this.mappedDrawable.edges);
    }
    iterateVias() {
        return this.iterateProp(this.mappedDrawable.vias);
    }
    getLayerById(id) {
        return this.layers.get(id);
    }
    /** Gets the group associated with a layer ID (it's one group per layer) */
    getLayerGroup(layer, createIfNotExists = false) {
        let layerGroup = this.layerGroups.get(layer);
        if (!layerGroup && createIfNotExists) {
            if (this.layers.has(layer)) {
                const maxLayer = maxA(...Array.from(this.layers.keys()));
                layerGroup = new Group(this.getLayerOrderForLayer(this.layers.get(layer), maxLayer), false);
            }
            else {
                layerGroup = new Group(layer, false);
            }
            const pixiLayer = new Layer$1(layerGroup);
            this.app.stage.addChild(pixiLayer);
            this.layerGroups.set(layer, layerGroup);
        }
        return layerGroup;
    }
    hasSelection() {
        return (this.selectedElements && this.selectedElements.size > 0) || this.selectedRectangle.hasSelection;
    }
    setVisibleLayers(layers, includeMetaLayers = false) {
        this.visibleLayers = layers;
        if (this.visibleLayers && !includeMetaLayers) {
            this.visibleLayers = [
                ...this.visibleLayers,
                ...(this.customOverlayLayers ? Array.from(this.customOverlayLayers.values()) : []),
                DrawingManager.SelectedElementsLayer,
                DrawingManager.OverlayLayer,
                DrawingManager.UserSelectionLayer,
                DrawingManager.BackgroundLayer,
            ];
        }
        let setLayers = this.visibleLayers;
        if (!setLayers) {
            setLayers = this.defaultVisibleLayers;
        }
        for (const element of this._elements) {
            element.setVisibleLayers(setLayers);
        }
    }
    getVisibleLayers() {
        return this.visibleLayers;
    }
    setDefaultVisibleLayers(layers) {
        this.defaultVisibleLayers = layers;
        if (!this.visibleLayers) {
            this.setVisibleLayers(this.defaultVisibleLayers);
        }
    }
    getLayerIsVisible(layer) {
        const visible = this.visibleLayers || this.defaultVisibleLayers;
        if (!visible) {
            return true;
        }
        return visible.indexOf(layer) !== -1;
    }
    *getLayersInUse() {
        for (const [layer] of this.layerGroups) {
            const layerInfo = this.getLayerById(layer);
            if (layerInfo) {
                yield layerInfo;
            }
        }
    }
    setLayerOrder(layersInOrderClosestToFurthest) {
        const maxZIndex = this.layerGroups.size * 10;
        const unchangeableLayers = [
            DrawingManager.SelectedElementsLayer,
            DrawingManager.OverlayLayer,
            DrawingManager.UserSelectionLayer,
            DrawingManager.BackgroundLayer,
        ];
        for (const [layer, group] of this.layerGroups.entries()) {
            if (unchangeableLayers.includes(layer)) {
                continue;
            }
            const pixiLayer = this.app.stage.children.filter((c) => c instanceof Layer$1 && c.group === group);
            if (layersInOrderClosestToFurthest.includes(layer)) {
                group.zIndex = maxZIndex - layersInOrderClosestToFurthest.indexOf(layer);
            }
            else {
                group.zIndex = 0;
            }
            pixiLayer.forEach((l) => (l.zIndex = group.zIndex));
        }
    }
    _selectElementsByProperty = (args) => {
        // Deselect without notifying: listeners must only ever see the resulting selection, never a
        // transient empty one that would race with the interaction state's own selection event.
        this.deselectAll();
        this.selectedElements = new Set();
        for (const element of this._elements) {
            element.selectProperties(args.allSelectionArgs);
            if (element.selected) {
                this.selectedElements.add(element);
            }
        }
        this.selectionChanged();
    };
    _clearSelection = () => {
        this.deselectAll();
        this.selectionChanged();
    };
    /**
     * Deselects every element and the selection rectangle without emitting `selectionChanged`.
     */
    deselectAll() {
        for (const element of this._elements) {
            element.setSelected(false, false);
        }
        this.selectedElements = undefined;
        this.selectedRectangle?.clearSelection();
    }
    highlightElementsByProperty = (args) => {
        for (const element of this._elements) {
            element.highlightProperties(args.allSelectionArgs);
        }
    };
    _clearHighlight = () => {
        for (const element of this._elements) {
            element.highlightProperties(undefined);
        }
    };
    setTagVisible(tag, visible, onLayer) {
        this.tagVisibilityState.set(tag, visible);
        for (const element of this._elements) {
            element.setTagVisible(tag, visible, onLayer);
        }
    }
    setTagDefaultVisibility(tag, onLayer) {
        this.tagVisibilityState.set(tag, 'default');
        for (const element of this._elements) {
            element.setTagDefaultVisibility(tag, onLayer);
        }
    }
    setAllTagDefaultVisibility(onLayer) {
        this.tagVisibilityState.clear();
        for (const element of this._elements) {
            element.setAllTagDefaultVisibility(onLayer);
        }
    }
    getDrawingTags() {
        return this.tags.keys();
    }
    _deselectElements(elements, otherSelections) {
        for (const element of elements) {
            element.setSelected(false, otherSelections);
        }
    }
    selectionChanged() {
        const selection = {
            mode: this.selectionMode,
            isEmpty: !this.hasSelection(),
            selectedElements: this.selectedElements,
            selectedProperties: this.interactionState?.getCurrentSelection(),
            selectionRect: this.selectedRectangle.hasSelection ? this.selectedRectangle : undefined,
        };
        this.emit('selectionChanged', selection, this);
    }
    selectionModeChanged(newMode) {
        if (this.selectionMode !== newMode) {
            this._selectionMode = newMode;
            this.emit('selectionModeChanged', this.selectionMode, this);
        }
    }
    mouseEventEmitter;
    onPointerOver = (_event) => {
        this.isMouseOver = true;
    };
    onPointerOut = (_event) => {
        this.isMouseOver = false;
    };
    addInteractiveElement(element) {
        if (this.interactiveElements.has(element.id)) {
            const el = this.interactiveElements.get(element.id);
            if (el !== element) {
                this.removeInteractiveElement(el);
                el.destroy();
            }
        }
        this.interactiveElements.set(element.id, element);
        element.drawingElement.parentGroup = this.layerGroups.get(DrawingManager.OverlayLayer);
        this.viewport.addChild(element.drawingElement);
    }
    tryRemoveInteractiveElementById(id) {
        const element = this.interactiveElements.get(id);
        if (element) {
            this.viewport.removeChild(element.drawingElement);
            this.interactiveElements.delete(element.id);
        }
        return element;
    }
    /**
     * Removes the interactive element from the drawing manager, but does not destroy it
     */
    removeInteractiveElement(element) {
        if (!this.interactiveElements.has(element.id)) {
            console.warn('Did not find interactive element: ' + element.id);
            return;
        }
        this.tryRemoveInteractiveElementById(element.id);
    }
    onPointerMove = (event) => {
        let parsedEvent;
        if (this.isMouseOver) {
            parsedEvent = this.eventToRectArgs(event, MouseEventButtons.None);
            this.mouseEventEmitter(parsedEvent);
        }
        if (this.potentialDragEvent && !this.dragging) {
            if (this.detectDragThreshold(event, parsedEvent)) {
                // Just passed the drag threshold - fire a drag event for the initial drag coordinates
                this.dragging = true;
                for (const element of this.interactiveElements.values()) {
                    if (element.enabled && element.onDragStart) {
                        element.onDragStart(this.potentialDragEvent, event);
                    }
                }
                this.potentialDragEvent = null;
            }
        }
        for (const element of this.interactiveElements.values()) {
            if (element.enabled && element.onPointerMove) {
                if (!parsedEvent) {
                    parsedEvent = this.eventToRectArgs(event, MouseEventButtons.None);
                }
                element.onPointerMove(parsedEvent, event);
            }
        }
    };
    onPointerUp = (event) => {
        const button = getMouseButtonForEvent(event);
        // Pointer down handler
        let parsedEvent;
        for (const element of this.interactiveElements.values()) {
            if (element.enabled && element.onPointerUp) {
                if (!parsedEvent) {
                    parsedEvent = this.eventToRectArgs(event, button);
                }
                element.onPointerUp(parsedEvent, event);
            }
        }
        if (this.potentialDragEvent) {
            this.potentialDragEvent = null;
        }
        // Only up handler for left click
        if (this.dragging && button === MouseEventButtons.Left) {
            this.dragging = false;
            if (this.disableViewportDragWhileDragging) {
                this.renderer.setDragOpts({
                    mouseButtons: 'middle-right',
                });
            }
            for (const element of this.interactiveElements.values()) {
                if (element.enabled && element.onDragEnd) {
                    if (!parsedEvent) {
                        parsedEvent = this.eventToRectArgs(event, button);
                    }
                    element.onDragEnd(parsedEvent, event);
                }
            }
        }
        if (this.selectionMode === SelectionMode.Rectangular && this.selectedRectangle.hasSelection) {
            this.selectionChanged();
        }
    };
    /** Store the 'pointerdown' event which may trigger a drag, so we can emit it if a drag happens. */
    potentialDragEvent = null;
    /**
     * {@link InteractiveDrawingElement}s only support one pointer at a time. This keeps track of which pointer that is.
     **/
    pointerId = null;
    onPointerDown = (event) => {
        const button = getMouseButtonForEvent(event);
        let parsedEvent;
        // Pointer down handler
        for (const element of this.interactiveElements.values()) {
            if (element.enabled && element.onPointerDown) {
                if (!parsedEvent) {
                    parsedEvent = this.eventToRectArgs(event, button);
                }
                element.onPointerDown(parsedEvent, event);
            }
        }
        // Only do drag handler for left click
        if (button !== MouseEventButtons.Left) {
            return;
        }
        if (!this.potentialDragEvent) {
            if (!parsedEvent) {
                parsedEvent = this.eventToRectArgs(event, button);
            }
            this.potentialDragEvent = parsedEvent;
        }
        if (!this.dragging) {
            if (this.detectDragThreshold(event, parsedEvent)) {
                // Just passed the drag threshold - fire a drag event for the initial drag coordinates
                this.dragging = true;
                for (const element of this.interactiveElements.values()) {
                    if (element.enabled && element.onDragStart) {
                        element.onDragStart(this.potentialDragEvent, event);
                    }
                }
                this.potentialDragEvent = null;
            }
            else {
                return;
            }
        }
        this.dragging = true;
        for (const element of this.interactiveElements.values()) {
            if (element.enabled && element.onDragStart) {
                if (!parsedEvent) {
                    parsedEvent = this.eventToRectArgs(event, button);
                }
                element.onDragStart(parsedEvent, event);
            }
        }
    };
    detectDragThreshold(event, parsedEvent = this.eventToRectArgs(event, getMouseButtonForEvent(event))) {
        if (this.dragging) {
            return true;
        }
        if (this.potentialDragEvent) {
            this.dragging =
                distanceBetweenIPoints(this.potentialDragEvent.screen, parsedEvent.screen) > DrawingManager.DragPixelThreshold;
        }
        return this.dragging;
    }
    disableViewportDragDuringCurrentDrag() {
        this.disableViewportDragWhileDragging = true;
        this.renderer.setDragOpts({
            mouseButtons: 'middle',
        });
    }
    /** Takes a pointer event and returns its info in a tidy object. */
    eventToRectArgs(event, button) {
        const bounds = this.viewport.getBounds();
        let screen;
        if (event.data.originalEvent.type.startsWith('touch')) {
            const touchEvent = event.data.originalEvent;
            if (touchEvent.touches?.length) {
                screen = {
                    x: touchEvent.touches[0].pageX,
                    y: touchEvent.touches[0].pageY,
                };
            }
            else {
                if (touchEvent.changedTouches?.length) {
                    screen = {
                        x: touchEvent.changedTouches[0].pageX,
                        y: touchEvent.changedTouches[0].pageY,
                    };
                }
                else {
                    console.warn("Couldn't find touch event");
                    screen = { x: 0, y: 0 };
                }
            }
        }
        else {
            const pointerEvent = event.data.originalEvent;
            screen = {
                x: pointerEvent.x,
                y: pointerEvent.y,
            };
        }
        return {
            world: this.viewport.toWorld(event.data.global.x, event.data.global.y),
            global: {
                x: event.data.global.x - bounds.x,
                y: event.data.global.y - bounds.y,
            },
            screen,
            button,
            shiftKey: Boolean(event.data.originalEvent.shiftKey),
        };
    }
    checkBoundsFinite(refresh) {
        const bounds = this.app.stage.getBounds(!refresh);
        return (Number.isFinite(bounds.bottom) &&
            Number.isFinite(bounds.top) &&
            Number.isFinite(bounds.left) &&
            Number.isFinite(bounds.right));
    }
    addElement(element) {
        const isFiniteAtStart = this.enableBoundsChecksOnRender && this.checkBoundsFinite(false);
        if (element.numGraphics === 0) {
            this.addRenderWarning('Refusing to add empty element');
            return;
        }
        this._elements.push(element);
        if (!this.mappedDrawable[element.modelObjectType]) {
            const m = new Map();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.mappedDrawable[element.modelObjectType] = m;
            m.set(element.modelObject, element);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const m = this.mappedDrawable[element.modelObjectType];
            m.set(element.modelObject, element);
        }
        for (const tag of element.tags()) {
            if (!this.tags.has(tag)) {
                this.tags.set(tag, [element]);
            }
            else {
                this.tags.get(tag).push(element);
            }
            if (this.tagVisibilityState.has(tag)) {
                const state = this.tagVisibilityState.get(tag);
                if (state !== 'default') {
                    element.setTagVisible(tag, state);
                }
            }
        }
        element.setVisibleLayers(this.visibleLayers || this.defaultVisibleLayers);
        this.viewport.addChild(element.container);
        element.seal();
        let boundEvents = false;
        if (element.isLink) {
            this.bindElementClickEvents(element);
            boundEvents = true;
        }
        if (!boundEvents) {
            this.bindElementClickEvents(element);
            boundEvents = true;
        }
        element.on('hoverstatechanged', (element) => this.handleElementHoverStateChanged(element));
        if (element.selectionType !== ObjectSelectionType.None) {
            if (!this.elementsSelectionTypes.has(element.selectionType)) {
                this.elementsSelectionTypes.set(element.selectionType, []);
            }
            this.elementsSelectionTypes.get(element.selectionType).push(element);
        }
        if (this.interactionState?.getCurrentSelection()) {
            element.selectProperties(this.interactionState.getCurrentSelection());
        }
        else {
            element.setSelected(false, Boolean(this.selectedElements));
        }
        if (isFiniteAtStart && !this.checkBoundsFinite(true)) {
            this.addRenderWarning(`Element caused bounds to become infinite`);
            console.warn(`Element caused bounds to become infinite`, element);
        }
    }
    bindElementClickEvents(element) {
        element.on('pointerdown', (event, element) => this.handlePointerDownOnElement(event, element));
        if (this.opts.enableDoubleClick) {
            element.on('dblclick', (event, element) => this.handleDblClickOnElement(event, element));
        }
    }
    handleElementHoverStateChanged(element) {
        const objectId = element.objectId;
        if (objectId) {
            if (element.isHovered) {
                this.interactionState?.highlightElementsByProperty(DrawingProperties.ObjectId, objectId, true);
            }
            else {
                this.interactionState?.unHighlightElementsByProperty(DrawingProperties.ObjectId, objectId);
            }
        }
        for (const net of element.nets()) {
            if (element.isHovered) {
                this.interactionState?.highlightElementsByProperty(DrawingProperties.Net, net, true);
            }
            else {
                this.interactionState?.unHighlightElementsByProperty(DrawingProperties.Net, net);
            }
        }
        this.emit('elementHoverStateChanged', element);
    }
    handlePointerDownOnElement(event, element) {
        if (element.selectionType === ObjectSelectionType.Net &&
            (this.selectionMode === SelectionMode.SingleNet || this.selectionMode === SelectionMode.AnySingleNetOrObject)) {
            const nets = Array.from(element.nets());
            for (const [index, net] of nets.entries()) {
                this.interactionState?.selectNet(net, index > 0);
            }
        }
        else if (element.selectionType === ObjectSelectionType.Object) {
            if (this.selectionMode === SelectionMode.SingleObject ||
                this.selectionMode === SelectionMode.MultipleObject ||
                this.selectionMode === SelectionMode.AnySingleNetOrObject) {
                if (element.objectId) {
                    this.interactionState?.selectElementsByProperty(DrawingProperties.ObjectId, element.objectId, this.selectionMode === SelectionMode.MultipleObject);
                }
            }
        }
        this.emit('elementPointerDown', event, element);
        if (element.isLink && this.linkHandlingMode === LinkHandlingMode.SingleClick) {
            this.handleLinkClicked(element);
        }
    }
    handleLinkClicked(element) {
        this.emit('linkClicked', element.linkDestination, element);
    }
    handleDblClickOnElement(_event, element) {
        this.emit('elementDoubleClicked', element);
        if (element.isLink && this.linkHandlingMode === LinkHandlingMode.DoubleClick) {
            this.handleLinkClicked(element);
        }
    }
    removeElement(element) {
        this._elements.splice(this._elements.indexOf(element), 1);
        this.mappedDrawable[element.modelObjectType]?.delete(element.modelObject);
        for (const tag of element.tags()) {
            const tagMap = this.tags.get(tag);
            tagMap.splice(tagMap.indexOf(element), 1);
            if (tagMap.length === 0) {
                this.tags.delete(tag);
            }
        }
        if (this.selectedElements?.has(element)) {
            this.selectedElements.delete(element);
            this.selectionChanged();
        }
        this.viewport.removeChild(element.container);
        element.removeAllListeners();
        element.destroy();
    }
    renderGrid(grid) {
        this._gridSettings = grid;
        this.prepareGrid();
        if (!this.isGridSupported) {
            return;
        }
        const bbox = this.renderer.getBoundsBbox();
        const obj = this.grid.render(expandRect({
            x1: bbox.minx,
            x2: bbox.maxx,
            y1: bbox.miny,
            y2: bbox.maxy,
        }, 10, undefined, false), grid);
        if (this.background.children.length === 0) {
            obj.scale.set(4);
            this.background.addChild(obj);
        }
    }
    unrenderGrid() {
        this.background.removeChildren();
    }
    prepareGrid() {
        if (!this.grid) {
            this.grid = new BackgroundGrid(this.viewport, this.colorProvider.getColorForTool(BackgroundGrid.ToolId), this.colorProvider.getColorForTool(BackgroundGrid.ToolId, BackgroundGrid.MajorToolOpt));
            this.grid.prepareShader(this.app.renderer);
        }
    }
    addRenderWarning(warning) {
        this.warnings.push(warning);
    }
    setDrawOverlayFunc(func) {
        this.drawOverlayFunc = func;
        this.overlays = new Map();
    }
    /**
     * Toggles visibility of the overlay
     * @param overlayId
     * @param visible
     */
    setOverlayVisible(overlayId, visible) {
        if (!this.overlays.has(overlayId)) {
            throw new Error(`Overlay with id '${overlayId}' does not exist`);
        }
        const overlay = this.overlays.get(overlayId);
        for (const element of overlay.elements) {
            element.setVisible(visible);
        }
    }
    /**
     * Adds an overlay to be rendered
     */
    addOverlay(overlay) {
        if (!this.drawOverlayFunc) {
            throw new Error('Must first register a draw overlay func');
        }
        if (this.overlays.has(overlay.id)) {
            this.removeOverlayInternal(overlay, this.overlays.get(overlay.id).elements);
        }
        let layer = DrawingManager.OverlayLayer;
        if (overlay.customOverlayLayerName) {
            layer = this.registerCustomOverlayLayer(overlay.customOverlayLayerName, overlay.customOverlayLayerNumber);
        }
        const elements = this.drawOverlayFunc(overlay, layer);
        this.overlays.set(overlay.id, new OverlayInternal(overlay, elements));
        this.emit('overlayAdded', overlay);
        return elements;
    }
    *getOverlays() {
        for (const overlay of this.overlays.values()) {
            yield overlay.overlay;
        }
    }
    /**
     * Zoom and centre an overlay, with padding as a fraction of the final viewport size.
     * @param overlayId
     * @param paddingFraction Fraction of the final viewport width/height to use as padding on each side.
     */
    zoomToOverlay(overlayId, paddingFraction = 0) {
        if (!this.overlays.has(overlayId)) {
            throw new Error(`Overlay id ${overlayId} not found`);
        }
        const overlay = this.overlays.get(overlayId);
        this.renderer.zoomAndCenterRect(overlay.getBoundingBox(), paddingFraction);
    }
    /**
     * Zoom and centre the current selection, with padding as a fraction of the final viewport size.
     * @param paddingFraction Fraction of the final viewport width/height to use as padding on each side.
     */
    zoomToSelection(paddingFraction = 0) {
        if (this.selectedElements?.size) {
            const boundingRect = normalizeRect(getBoundingRect(Array.from(this.selectedElements).map((e) => e.getSelectionBounds())));
            this.renderer.zoomAndCenterRect(boundingRect, paddingFraction);
        }
        else if (this.selectedRectangle.hasSelection) {
            this.renderer.zoomAndCenterRect(this.selectedRectangle.rendererCoords(), paddingFraction);
        }
    }
    /**
     * Adds or gets a custom overlay layer.
     *
     * If a layer with that name already exists, returns the layer number.
     * @param layerName
     * @returns The layer number to use
     */
    registerCustomOverlayLayer(layerName, layerNum) {
        if (!this.customOverlayLayers) {
            this.customOverlayLayers = new Map();
        }
        if (!this.customOverlayLayers.has(layerName)) {
            const newLayerNumber = typeof layerNum === 'number' ? layerNum : DrawingManager.OverlayLayer + 1 + this.customOverlayLayers.size;
            this.customOverlayLayers.set(layerName, newLayerNumber);
            this.createLayerGroup(newLayerNumber);
        }
        return this.customOverlayLayers.get(layerName);
    }
    /**
     * Removes an overlay
     * @param overlayId
     */
    removeOverlay(overlayId) {
        if (!this.overlays.has(overlayId)) {
            return;
        }
        const overlay = this.overlays.get(overlayId);
        this.removeOverlayInternal(overlay.overlay, overlay.elements);
    }
    /**
     * Enables or disables the render
     */
    setRenderEnabled(enabled) {
        if (enabled) {
            this.app.start();
        }
        else {
            this.app.stop();
        }
    }
    removeOverlayInternal(overlay, elements) {
        this.overlays.delete(overlay.id);
        for (const element of elements) {
            this.removeElement(element);
        }
    }
    destroy() {
        this._isDestroyed = true;
        this.textureManager.destroy();
        if (this.grid) {
            this.unrenderGrid();
            this.grid.destroy();
            this.grid = undefined;
        }
        this.interactionState?.removeListener('clearSelection', this._clearSelection);
        this.interactionState?.removeListener('selectElementsByProperty', this._selectElementsByProperty);
        this.interactionState?.removeListener('clearHighlight', this._clearHighlight);
        this.interactionState?.removeListener('highlightElementsByProperty', this.highlightElementsByProperty);
        for (const element of this._elements) {
            element.removeAllListeners();
            element.destroy();
        }
        for (const drawingElement of this.interactiveElements.values()) {
            this.removeInteractiveElement(drawingElement);
            drawingElement.destroy();
        }
        this.interactiveElements.clear();
        this.app.stage
            .removeListener('pointerdown', this.onPointerDown)
            .removeListener('pointerup', this.onPointerUp)
            .removeListener('pointerupoutside', this.onPointerUp);
        this.app?.stage?.destroy();
    }
    on(eventName, listener) {
        return super.on(eventName, listener);
    }
    removeListener(eventName, listener) {
        return super.removeListener(eventName, listener);
    }
    createLayerGroup(layerNumber) {
        const overlayGroup = new Group(layerNumber);
        this.app.stage.addChild(new Layer$1(overlayGroup));
        this.layerGroups.set(layerNumber, overlayGroup);
    }
    /**
     * For a given design layer, return the Pixi layer it should be rendered on.
     *
     * Each design layer is itself made up of multiple layers (described by the {@link LayerType} type), so this method
     * designates a Pixi layer for all the "sub-layers" of each design layer.
     *
     * It's evident we need better terminology for this.
     */
    getLayerOrderForLayer(layer, maxLayer) {
        let baseMax;
        switch (layer.face) {
            case 'F':
                baseMax = 3 * maxLayer;
                break;
            case 'B':
                baseMax = maxLayer;
                break;
            default:
                baseMax = 2 * maxLayer;
                break;
        }
        return baseMax - layer.id;
    }
}

const colors = {
	aliceblue: [240, 248, 255],
	antiquewhite: [250, 235, 215],
	aqua: [0, 255, 255],
	aquamarine: [127, 255, 212],
	azure: [240, 255, 255],
	beige: [245, 245, 220],
	bisque: [255, 228, 196],
	black: [0, 0, 0],
	blanchedalmond: [255, 235, 205],
	blue: [0, 0, 255],
	blueviolet: [138, 43, 226],
	brown: [165, 42, 42],
	burlywood: [222, 184, 135],
	cadetblue: [95, 158, 160],
	chartreuse: [127, 255, 0],
	chocolate: [210, 105, 30],
	coral: [255, 127, 80],
	cornflowerblue: [100, 149, 237],
	cornsilk: [255, 248, 220],
	crimson: [220, 20, 60],
	cyan: [0, 255, 255],
	darkblue: [0, 0, 139],
	darkcyan: [0, 139, 139],
	darkgoldenrod: [184, 134, 11],
	darkgray: [169, 169, 169],
	darkgreen: [0, 100, 0],
	darkgrey: [169, 169, 169],
	darkkhaki: [189, 183, 107],
	darkmagenta: [139, 0, 139],
	darkolivegreen: [85, 107, 47],
	darkorange: [255, 140, 0],
	darkorchid: [153, 50, 204],
	darkred: [139, 0, 0],
	darksalmon: [233, 150, 122],
	darkseagreen: [143, 188, 143],
	darkslateblue: [72, 61, 139],
	darkslategray: [47, 79, 79],
	darkslategrey: [47, 79, 79],
	darkturquoise: [0, 206, 209],
	darkviolet: [148, 0, 211],
	deeppink: [255, 20, 147],
	deepskyblue: [0, 191, 255],
	dimgray: [105, 105, 105],
	dimgrey: [105, 105, 105],
	dodgerblue: [30, 144, 255],
	firebrick: [178, 34, 34],
	floralwhite: [255, 250, 240],
	forestgreen: [34, 139, 34],
	fuchsia: [255, 0, 255],
	gainsboro: [220, 220, 220],
	ghostwhite: [248, 248, 255],
	gold: [255, 215, 0],
	goldenrod: [218, 165, 32],
	gray: [128, 128, 128],
	green: [0, 128, 0],
	greenyellow: [173, 255, 47],
	grey: [128, 128, 128],
	honeydew: [240, 255, 240],
	hotpink: [255, 105, 180],
	indianred: [205, 92, 92],
	indigo: [75, 0, 130],
	ivory: [255, 255, 240],
	khaki: [240, 230, 140],
	lavender: [230, 230, 250],
	lavenderblush: [255, 240, 245],
	lawngreen: [124, 252, 0],
	lemonchiffon: [255, 250, 205],
	lightblue: [173, 216, 230],
	lightcoral: [240, 128, 128],
	lightcyan: [224, 255, 255],
	lightgoldenrodyellow: [250, 250, 210],
	lightgray: [211, 211, 211],
	lightgreen: [144, 238, 144],
	lightgrey: [211, 211, 211],
	lightpink: [255, 182, 193],
	lightsalmon: [255, 160, 122],
	lightseagreen: [32, 178, 170],
	lightskyblue: [135, 206, 250],
	lightslategray: [119, 136, 153],
	lightslategrey: [119, 136, 153],
	lightsteelblue: [176, 196, 222],
	lightyellow: [255, 255, 224],
	lime: [0, 255, 0],
	limegreen: [50, 205, 50],
	linen: [250, 240, 230],
	magenta: [255, 0, 255],
	maroon: [128, 0, 0],
	mediumaquamarine: [102, 205, 170],
	mediumblue: [0, 0, 205],
	mediumorchid: [186, 85, 211],
	mediumpurple: [147, 112, 219],
	mediumseagreen: [60, 179, 113],
	mediumslateblue: [123, 104, 238],
	mediumspringgreen: [0, 250, 154],
	mediumturquoise: [72, 209, 204],
	mediumvioletred: [199, 21, 133],
	midnightblue: [25, 25, 112],
	mintcream: [245, 255, 250],
	mistyrose: [255, 228, 225],
	moccasin: [255, 228, 181],
	navajowhite: [255, 222, 173],
	navy: [0, 0, 128],
	oldlace: [253, 245, 230],
	olive: [128, 128, 0],
	olivedrab: [107, 142, 35],
	orange: [255, 165, 0],
	orangered: [255, 69, 0],
	orchid: [218, 112, 214],
	palegoldenrod: [238, 232, 170],
	palegreen: [152, 251, 152],
	paleturquoise: [175, 238, 238],
	palevioletred: [219, 112, 147],
	papayawhip: [255, 239, 213],
	peachpuff: [255, 218, 185],
	peru: [205, 133, 63],
	pink: [255, 192, 203],
	plum: [221, 160, 221],
	powderblue: [176, 224, 230],
	purple: [128, 0, 128],
	rebeccapurple: [102, 51, 153],
	red: [255, 0, 0],
	rosybrown: [188, 143, 143],
	royalblue: [65, 105, 225],
	saddlebrown: [139, 69, 19],
	salmon: [250, 128, 114],
	sandybrown: [244, 164, 96],
	seagreen: [46, 139, 87],
	seashell: [255, 245, 238],
	sienna: [160, 82, 45],
	silver: [192, 192, 192],
	skyblue: [135, 206, 235],
	slateblue: [106, 90, 205],
	slategray: [112, 128, 144],
	slategrey: [112, 128, 144],
	snow: [255, 250, 250],
	springgreen: [0, 255, 127],
	steelblue: [70, 130, 180],
	tan: [210, 180, 140],
	teal: [0, 128, 128],
	thistle: [216, 191, 216],
	tomato: [255, 99, 71],
	turquoise: [64, 224, 208],
	violet: [238, 130, 238],
	wheat: [245, 222, 179],
	white: [255, 255, 255],
	whitesmoke: [245, 245, 245],
	yellow: [255, 255, 0],
	yellowgreen: [154, 205, 50],
};

for (const key in colors) Object.freeze(colors[key]);
var cssKeywords = Object.freeze(colors);

const reverseNames = Object.create(null);

// Create a list of reverse color names
for (const name in cssKeywords) {
	if (Object.hasOwn(cssKeywords, name)) {
		reverseNames[cssKeywords[name]] = name;
	}
}

const cs = {
	to: {},
	get: {},
};

cs.get = function (string) {
	const prefix = string.slice(0, 3).toLowerCase();
	let value;
	let model;
	switch (prefix) {
		case 'hsl': {
			value = cs.get.hsl(string);
			model = 'hsl';
			break;
		}

		case 'hwb': {
			value = cs.get.hwb(string);
			model = 'hwb';
			break;
		}

		default: {
			value = cs.get.rgb(string);
			model = 'rgb';
			break;
		}
	}

	if (!value) {
		return null;
	}

	return {model, value};
};

cs.get.rgb = function (string) {
	if (!string) {
		return null;
	}

	const abbr = /^#([a-f\d]{3,4})$/i;
	const hex = /^#([a-f\d]{6})([a-f\d]{2})?$/i;
	const rgba = /^rgba?\(\s*([+-]?(?:\d*\.)?\d+(?:e\d+)?)(?=[\s,])\s*(?:,\s*)?([+-]?(?:\d*\.)?\d+(?:e\d+)?)(?=[\s,])\s*(?:,\s*)?([+-]?(?:\d*\.)?\d+(?:e\d+)?)\s*(?:[\s,|/]\s*([+-]?(?:\d*\.)?\d+(?:e\d+)?)(%?)\s*)?\)$/i;
	const per = /^rgba?\(\s*([+-]?[\d.]+)%\s*,?\s*([+-]?[\d.]+)%\s*,?\s*([+-]?[\d.]+)%\s*(?:[\s,|/]\s*([+-]?[\d.]+)(%?)\s*)?\)$/i;
	const keyword = /^(\w+)$/;

	let rgb = [0, 0, 0, 1];
	let match;
	let i;
	let hexAlpha;

	if (match = string.match(hex)) {
		hexAlpha = match[2];
		match = match[1];

		for (i = 0; i < 3; i++) {
			// https://jsperf.com/slice-vs-substr-vs-substring-methods-long-string/19
			const i2 = i * 2;
			rgb[i] = Number.parseInt(match.slice(i2, i2 + 2), 16);
		}

		if (hexAlpha) {
			rgb[3] = Number.parseInt(hexAlpha, 16) / 255;
		}
	} else if (match = string.match(abbr)) {
		match = match[1];
		hexAlpha = match[3];

		for (i = 0; i < 3; i++) {
			rgb[i] = Number.parseInt(match[i] + match[i], 16);
		}

		if (hexAlpha) {
			rgb[3] = Number.parseInt(hexAlpha + hexAlpha, 16) / 255;
		}
	} else if (match = string.match(rgba)) {
		for (i = 0; i < 3; i++) {
			rgb[i] = Number.parseFloat(match[i + 1]);
		}

		if (match[4]) {
			rgb[3] = match[5] ? Number.parseFloat(match[4]) * 0.01 : Number.parseFloat(match[4]);
		}
	} else if (match = string.match(per)) {
		for (i = 0; i < 3; i++) {
			rgb[i] = Math.round(Number.parseFloat(match[i + 1]) * 2.55);
		}

		if (match[4]) {
			rgb[3] = match[5] ? Number.parseFloat(match[4]) * 0.01 : Number.parseFloat(match[4]);
		}
	} else if (match = string.toLowerCase().match(keyword)) {
		if (match[1] === 'transparent') {
			return [0, 0, 0, 0];
		}

		if (!Object.hasOwn(cssKeywords, match[1])) {
			return null;
		}

		// eslint-disable-next-line unicorn/prefer-spread
		rgb = cssKeywords[match[1]].slice();
		rgb[3] = 1;

		return rgb;
	} else {
		return null;
	}

	for (i = 0; i < 3; i++) {
		rgb[i] = clamp(rgb[i], 0, 255);
	}

	rgb[3] = clamp(rgb[3], 0, 1);

	return rgb;
};

cs.get.hsl = function (string) {
	if (!string) {
		return null;
	}

	const hsl = /^hsla?\(\s*([+-]?(?:\d{0,3}\.)?\d+)(?:deg)?\s*,?\s*([+-]?[\d.]+)%\s*,?\s*([+-]?[\d.]+)%\s*(?:[,|/]\s*([+-]?(?=\.\d|\d)(?:0|[1-9]\d*)?(?:\.\d*)?(?:e[+-]?\d+)?)\s*)?\)$/i;
	const match = string.match(hsl);

	if (match) {
		const alpha = Number.parseFloat(match[4]);
		const h = ((Number.parseFloat(match[1]) % 360) + 360) % 360;
		const s = clamp(Number.parseFloat(match[2]), 0, 100);
		const l = clamp(Number.parseFloat(match[3]), 0, 100);
		const a = clamp(Number.isNaN(alpha) ? 1 : alpha, 0, 1);

		return [h, s, l, a];
	}

	return null;
};

cs.get.hwb = function (string) {
	if (!string) {
		return null;
	}

	const hwb = /^hwb\(\s*([+-]?\d{0,3}(?:\.\d+)?)(?:deg)?\s*[\s,]\s*([+-]?[\d.]+)%\s*[\s,]\s*([+-]?[\d.]+)%\s*(?:[\s,]\s*([+-]?(?=\.\d|\d)(?:0|[1-9]\d*)?(?:\.\d*)?(?:e[+-]?\d+)?)\s*)?\)$/i;
	const match = string.match(hwb);

	if (match) {
		const alpha = Number.parseFloat(match[4]);
		const h = ((Number.parseFloat(match[1]) % 360) + 360) % 360;
		const w = clamp(Number.parseFloat(match[2]), 0, 100);
		const b = clamp(Number.parseFloat(match[3]), 0, 100);
		const a = clamp(Number.isNaN(alpha) ? 1 : alpha, 0, 1);
		return [h, w, b, a];
	}

	return null;
};

cs.to.hex = function (...rgba) {
	return (
		'#' +
		hexDouble(rgba[0]) +
		hexDouble(rgba[1]) +
		hexDouble(rgba[2]) +
		(rgba[3] < 1
			? (hexDouble(Math.round(rgba[3] * 255)))
			: '')
	);
};

cs.to.rgb = function (...rgba) {
	return rgba.length < 4 || rgba[3] === 1
		? 'rgb(' + Math.round(rgba[0]) + ', ' + Math.round(rgba[1]) + ', ' + Math.round(rgba[2]) + ')'
		: 'rgba(' + Math.round(rgba[0]) + ', ' + Math.round(rgba[1]) + ', ' + Math.round(rgba[2]) + ', ' + rgba[3] + ')';
};

cs.to.rgb.percent = function (...rgba) {
	const r = Math.round(rgba[0] / 255 * 100);
	const g = Math.round(rgba[1] / 255 * 100);
	const b = Math.round(rgba[2] / 255 * 100);

	return rgba.length < 4 || rgba[3] === 1
		? 'rgb(' + r + '%, ' + g + '%, ' + b + '%)'
		: 'rgba(' + r + '%, ' + g + '%, ' + b + '%, ' + rgba[3] + ')';
};

cs.to.hsl = function (...hsla) {
	return hsla.length < 4 || hsla[3] === 1
		? 'hsl(' + hsla[0] + ', ' + hsla[1] + '%, ' + hsla[2] + '%)'
		: 'hsla(' + hsla[0] + ', ' + hsla[1] + '%, ' + hsla[2] + '%, ' + hsla[3] + ')';
};

// Hwb is a bit different than rgb(a) & hsl(a) since there is no alpha specific syntax
// (hwb have alpha optional & 1 is default value)
cs.to.hwb = function (...hwba) {
	let a = '';
	if (hwba.length >= 4 && hwba[3] !== 1) {
		a = ', ' + hwba[3];
	}

	return 'hwb(' + hwba[0] + ', ' + hwba[1] + '%, ' + hwba[2] + '%' + a + ')';
};

cs.to.keyword = function (...rgb) {
	return reverseNames[rgb.slice(0, 3)];
};

// Helpers
function clamp(number_, min, max) {
	return Math.min(Math.max(min, number_), max);
}

function hexDouble(number_) {
	const string_ = Math.round(number_).toString(16).toUpperCase();
	return (string_.length < 2) ? '0' + string_ : string_;
}

/* MIT license */

// NOTE: conversions should only return primitive values (i.e. arrays, or
//       values that give correct `typeof` results).
//       do not use box values types (i.e. Number(), String(), etc.)

const reverseKeywords = {};
for (const key of Object.keys(cssKeywords)) {
	reverseKeywords[cssKeywords[key]] = key;
}

const convert$1 = {
	rgb: {channels: 3, labels: 'rgb'},
	hsl: {channels: 3, labels: 'hsl'},
	hsv: {channels: 3, labels: 'hsv'},
	hwb: {channels: 3, labels: 'hwb'},
	cmyk: {channels: 4, labels: 'cmyk'},
	xyz: {channels: 3, labels: 'xyz'},
	lab: {channels: 3, labels: 'lab'},
	oklab: {channels: 3, labels: ['okl', 'oka', 'okb']},
	lch: {channels: 3, labels: 'lch'},
	oklch: {channels: 3, labels: ['okl', 'okc', 'okh']},
	hex: {channels: 1, labels: ['hex']},
	keyword: {channels: 1, labels: ['keyword']},
	ansi16: {channels: 1, labels: ['ansi16']},
	ansi256: {channels: 1, labels: ['ansi256']},
	hcg: {channels: 3, labels: ['h', 'c', 'g']},
	apple: {channels: 3, labels: ['r16', 'g16', 'b16']},
	gray: {channels: 1, labels: ['gray']},
};

// LAB f(t) constant
const LAB_FT = (6 / 29) ** 3;

// SRGB non-linear transform functions
function srgbNonlinearTransform(c) {
	const cc = c > 0.003_130_8
		? ((1.055 * (c ** (1 / 2.4))) - 0.055)
		: c * 12.92;
	return Math.min(Math.max(0, cc), 1);
}

function srgbNonlinearTransformInv(c) {
	return c > 0.040_45 ? (((c + 0.055) / 1.055) ** 2.4) : (c / 12.92);
}

// Hide .channels and .labels properties
for (const model of Object.keys(convert$1)) {
	if (!('channels' in convert$1[model])) {
		throw new Error('missing channels property: ' + model);
	}

	if (!('labels' in convert$1[model])) {
		throw new Error('missing channel labels property: ' + model);
	}

	if (convert$1[model].labels.length !== convert$1[model].channels) {
		throw new Error('channel and label counts mismatch: ' + model);
	}

	const {channels, labels} = convert$1[model];
	delete convert$1[model].channels;
	delete convert$1[model].labels;
	Object.defineProperty(convert$1[model], 'channels', {value: channels});
	Object.defineProperty(convert$1[model], 'labels', {value: labels});
}

convert$1.rgb.hsl = function (rgb) {
	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;
	const min = Math.min(r, g, b);
	const max = Math.max(r, g, b);
	const delta = max - min;
	let h;
	let s;

	switch (max) {
		case min: {
			h = 0;

			break;
		}

		case r: {
			h = (g - b) / delta;

			break;
		}

		case g: {
			h = 2 + (b - r) / delta;

			break;
		}

		case b: {
			h = 4 + (r - g) / delta;

			break;
		}
	// No default
	}

	h = Math.min(h * 60, 360);

	if (h < 0) {
		h += 360;
	}

	const l = (min + max) / 2;

	if (max === min) {
		s = 0;
	} else if (l <= 0.5) {
		s = delta / (max + min);
	} else {
		s = delta / (2 - max - min);
	}

	return [h, s * 100, l * 100];
};

convert$1.rgb.hsv = function (rgb) {
	let rdif;
	let gdif;
	let bdif;
	let h;
	let s;

	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;
	const v = Math.max(r, g, b);
	const diff = v - Math.min(r, g, b);
	const diffc = function (c) {
		return (v - c) / 6 / diff + 1 / 2;
	};

	if (diff === 0) {
		h = 0;
		s = 0;
	} else {
		s = diff / v;
		rdif = diffc(r);
		gdif = diffc(g);
		bdif = diffc(b);

		switch (v) {
			case r: {
				h = bdif - gdif;

				break;
			}

			case g: {
				h = (1 / 3) + rdif - bdif;

				break;
			}

			case b: {
				h = (2 / 3) + gdif - rdif;

				break;
			}
		// No default
		}

		if (h < 0) {
			h += 1;
		} else if (h > 1) {
			h -= 1;
		}
	}

	return [
		h * 360,
		s * 100,
		v * 100,
	];
};

convert$1.rgb.hwb = function (rgb) {
	const r = rgb[0];
	const g = rgb[1];
	let b = rgb[2];
	const h = convert$1.rgb.hsl(rgb)[0];
	const w = 1 / 255 * Math.min(r, Math.min(g, b));

	b = 1 - 1 / 255 * Math.max(r, Math.max(g, b));

	return [h, w * 100, b * 100];
};

convert$1.rgb.oklab = function (rgb) {
	// Assume sRGB
	const r = srgbNonlinearTransformInv(rgb[0] / 255);
	const g = srgbNonlinearTransformInv(rgb[1] / 255);
	const b = srgbNonlinearTransformInv(rgb[2] / 255);

	const lp = Math.cbrt(0.412_221_470_8 * r + 0.536_332_536_3 * g + 0.051_445_992_9 * b);
	const mp = Math.cbrt(0.211_903_498_2 * r + 0.680_699_545_1 * g + 0.107_396_956_6 * b);
	const sp = Math.cbrt(0.088_302_461_9 * r + 0.281_718_837_6 * g + 0.629_978_700_5 * b);

	const l = 0.210_454_255_3 * lp + 0.793_617_785 * mp - 0.004_072_046_8 * sp;
	const aa = 1.977_998_495_1 * lp - 2.428_592_205 * mp + 0.450_593_709_9 * sp;
	const bb = 0.025_904_037_1 * lp + 0.782_771_766_2 * mp - 0.808_675_766 * sp;

	return [l * 100, aa * 100, bb * 100];
};

convert$1.rgb.cmyk = function (rgb) {
	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;

	const k = Math.min(1 - r, 1 - g, 1 - b);
	const c = (1 - r - k) / (1 - k) || 0;
	const m = (1 - g - k) / (1 - k) || 0;
	const y = (1 - b - k) / (1 - k) || 0;

	return [c * 100, m * 100, y * 100, k * 100];
};

function comparativeDistance(x, y) {
	/*
		See https://en.m.wikipedia.org/wiki/Euclidean_distance#Squared_Euclidean_distance
	*/
	return (
		((x[0] - y[0]) ** 2) +
		((x[1] - y[1]) ** 2) +
		((x[2] - y[2]) ** 2)
	);
}

convert$1.rgb.keyword = function (rgb) {
	const reversed = reverseKeywords[rgb];
	if (reversed) {
		return reversed;
	}

	let currentClosestDistance = Number.POSITIVE_INFINITY;
	let currentClosestKeyword;

	for (const keyword of Object.keys(cssKeywords)) {
		const value = cssKeywords[keyword];

		// Compute comparative distance
		const distance = comparativeDistance(rgb, value);

		// Check if its less, if so set as closest
		if (distance < currentClosestDistance) {
			currentClosestDistance = distance;
			currentClosestKeyword = keyword;
		}
	}

	return currentClosestKeyword;
};

convert$1.keyword.rgb = function (keyword) {
	return [...cssKeywords[keyword]];
};

convert$1.rgb.xyz = function (rgb) {
	// Assume sRGB
	const r = srgbNonlinearTransformInv(rgb[0] / 255);
	const g = srgbNonlinearTransformInv(rgb[1] / 255);
	const b = srgbNonlinearTransformInv(rgb[2] / 255);

	const x = (r * 0.412_456_4) + (g * 0.357_576_1) + (b * 0.180_437_5);
	const y = (r * 0.212_672_9) + (g * 0.715_152_2) + (b * 0.072_175);
	const z = (r * 0.019_333_9) + (g * 0.119_192) + (b * 0.950_304_1);

	return [x * 100, y * 100, z * 100];
};

convert$1.rgb.lab = function (rgb) {
	const xyz = convert$1.rgb.xyz(rgb);
	let x = xyz[0];
	let y = xyz[1];
	let z = xyz[2];

	x /= 95.047;
	y /= 100;
	z /= 108.883;

	x = x > LAB_FT ? (x ** (1 / 3)) : (7.787 * x) + (16 / 116);
	y = y > LAB_FT ? (y ** (1 / 3)) : (7.787 * y) + (16 / 116);
	z = z > LAB_FT ? (z ** (1 / 3)) : (7.787 * z) + (16 / 116);

	const l = (116 * y) - 16;
	const a = 500 * (x - y);
	const b = 200 * (y - z);

	return [l, a, b];
};

convert$1.hsl.rgb = function (hsl) {
	const h = hsl[0] / 360;
	const s = hsl[1] / 100;
	const l = hsl[2] / 100;
	let t3;
	let value;

	if (s === 0) {
		value = l * 255;
		return [value, value, value];
	}

	const t2 = l < 0.5 ? l * (1 + s) : l + s - l * s;

	const t1 = 2 * l - t2;

	const rgb = [0, 0, 0];
	for (let i = 0; i < 3; i++) {
		t3 = h + 1 / 3 * -(i - 1);
		if (t3 < 0) {
			t3++;
		}

		if (t3 > 1) {
			t3--;
		}

		if (6 * t3 < 1) {
			value = t1 + (t2 - t1) * 6 * t3;
		} else if (2 * t3 < 1) {
			value = t2;
		} else if (3 * t3 < 2) {
			value = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
		} else {
			value = t1;
		}

		rgb[i] = value * 255;
	}

	return rgb;
};

convert$1.hsl.hsv = function (hsl) {
	const h = hsl[0];
	let s = hsl[1] / 100;
	let l = hsl[2] / 100;
	let smin = s;
	const lmin = Math.max(l, 0.01);

	l *= 2;
	s *= (l <= 1) ? l : 2 - l;
	smin *= lmin <= 1 ? lmin : 2 - lmin;
	const v = (l + s) / 2;
	const sv = l === 0 ? (2 * smin) / (lmin + smin) : (2 * s) / (l + s);

	return [h, sv * 100, v * 100];
};

convert$1.hsv.rgb = function (hsv) {
	const h = hsv[0] / 60;
	const s = hsv[1] / 100;
	let v = hsv[2] / 100;
	const hi = Math.floor(h) % 6;

	const f = h - Math.floor(h);
	const p = 255 * v * (1 - s);
	const q = 255 * v * (1 - (s * f));
	const t = 255 * v * (1 - (s * (1 - f)));
	v *= 255;

	switch (hi) {
		case 0: {
			return [v, t, p];
		}

		case 1: {
			return [q, v, p];
		}

		case 2: {
			return [p, v, t];
		}

		case 3: {
			return [p, q, v];
		}

		case 4: {
			return [t, p, v];
		}

		case 5: {
			return [v, p, q];
		}
	}
};

convert$1.hsv.hsl = function (hsv) {
	const h = hsv[0];
	const s = hsv[1] / 100;
	const v = hsv[2] / 100;
	const vmin = Math.max(v, 0.01);
	let sl;
	let l;

	l = (2 - s) * v;
	const lmin = (2 - s) * vmin;
	sl = s * vmin;
	sl /= (lmin <= 1) ? lmin : 2 - lmin;
	sl = sl || 0;
	l /= 2;

	return [h, sl * 100, l * 100];
};

// http://dev.w3.org/csswg/css-color/#hwb-to-rgb
convert$1.hwb.rgb = function (hwb) {
	const h = hwb[0] / 360;
	let wh = hwb[1] / 100;
	let bl = hwb[2] / 100;
	const ratio = wh + bl;
	let f;

	// Wh + bl cant be > 1
	if (ratio > 1) {
		wh /= ratio;
		bl /= ratio;
	}

	const i = Math.floor(6 * h);
	const v = 1 - bl;
	f = 6 * h - i;

	// eslint-disable-next-line no-bitwise
	if ((i & 0x01) !== 0) {
		f = 1 - f;
	}

	const n = wh + f * (v - wh); // Linear interpolation

	let r;
	let g;
	let b;
	/* eslint-disable max-statements-per-line,no-multi-spaces, default-case-last */
	switch (i) {
		default:
		case 6:
		case 0: { r = v;  g = n;  b = wh; break;
		}

		case 1: { r = n;  g = v;  b = wh; break;
		}

		case 2: { r = wh; g = v;  b = n; break;
		}

		case 3: { r = wh; g = n;  b = v; break;
		}

		case 4: { r = n;  g = wh; b = v; break;
		}

		case 5: { r = v;  g = wh; b = n; break;
		}
	}
	/* eslint-enable max-statements-per-line,no-multi-spaces, default-case-last */

	return [r * 255, g * 255, b * 255];
};

convert$1.cmyk.rgb = function (cmyk) {
	const c = cmyk[0] / 100;
	const m = cmyk[1] / 100;
	const y = cmyk[2] / 100;
	const k = cmyk[3] / 100;

	const r = 1 - Math.min(1, c * (1 - k) + k);
	const g = 1 - Math.min(1, m * (1 - k) + k);
	const b = 1 - Math.min(1, y * (1 - k) + k);

	return [r * 255, g * 255, b * 255];
};

convert$1.xyz.rgb = function (xyz) {
	const x = xyz[0] / 100;
	const y = xyz[1] / 100;
	const z = xyz[2] / 100;
	let r;
	let g;
	let b;

	r = (x * 3.240_454_2) + (y * -1.537_138_5) + (z * -0.498_531_4);
	g = (x * -0.969_266) + (y * 1.876_010_8) + (z * 0.041_556);
	b = (x * 0.055_643_4) + (y * -0.204_025_9) + (z * 1.057_225_2);

	// Assume sRGB
	r = srgbNonlinearTransform(r);
	g = srgbNonlinearTransform(g);
	b = srgbNonlinearTransform(b);

	return [r * 255, g * 255, b * 255];
};

convert$1.xyz.lab = function (xyz) {
	let x = xyz[0];
	let y = xyz[1];
	let z = xyz[2];

	x /= 95.047;
	y /= 100;
	z /= 108.883;

	x = x > LAB_FT ? (x ** (1 / 3)) : (7.787 * x) + (16 / 116);
	y = y > LAB_FT ? (y ** (1 / 3)) : (7.787 * y) + (16 / 116);
	z = z > LAB_FT ? (z ** (1 / 3)) : (7.787 * z) + (16 / 116);

	const l = (116 * y) - 16;
	const a = 500 * (x - y);
	const b = 200 * (y - z);

	return [l, a, b];
};

convert$1.xyz.oklab = function (xyz) {
	const x = xyz[0] / 100;
	const y = xyz[1] / 100;
	const z = xyz[2] / 100;

	const lp = Math.cbrt(0.818_933_010_1 * x + 0.361_866_742_4 * y - 0.128_859_713_7 * z);
	const mp = Math.cbrt(0.032_984_543_6 * x + 0.929_311_871_5 * y + 0.036_145_638_7 * z);
	const sp = Math.cbrt(0.048_200_301_8 * x + 0.264_366_269_1 * y + 0.633_851_707 * z);

	const l = 0.210_454_255_3 * lp + 0.793_617_785 * mp - 0.004_072_046_8 * sp;
	const a = 1.977_998_495_1 * lp - 2.428_592_205 * mp + 0.450_593_709_9 * sp;
	const b = 0.025_904_037_1 * lp + 0.782_771_766_2 * mp - 0.808_675_766 * sp;

	return [l * 100, a * 100, b * 100];
};

convert$1.oklab.oklch = function (oklab) {
	return convert$1.lab.lch(oklab);
};

convert$1.oklab.xyz = function (oklab) {
	const ll = oklab[0] / 100;
	const a = oklab[1] / 100;
	const b = oklab[2] / 100;

	const l = (0.999_999_998 * ll + 0.396_337_792 * a + 0.215_803_758 * b) ** 3;
	const m = (1.000_000_008 * ll - 0.105_561_342 * a - 0.063_854_175 * b) ** 3;
	const s = (1.000_000_055 * ll - 0.089_484_182 * a - 1.291_485_538 * b) ** 3;

	const x = 1.227_013_851 * l - 0.557_799_98 * m + 0.281_256_149 * s;
	const y = -0.040_580_178 * l + 1.112_256_87 * m - 0.071_676_679 * s;
	const z = -0.076_381_285 * l - 0.421_481_978 * m + 1.586_163_22 * s;

	return [x * 100, y * 100, z * 100];
};

convert$1.oklab.rgb = function (oklab) {
	const ll = oklab[0] / 100;
	const aa = oklab[1] / 100;
	const bb = oklab[2] / 100;

	const l = (ll + 0.396_337_777_4 * aa + 0.215_803_757_3 * bb) ** 3;
	const m = (ll - 0.105_561_345_8 * aa - 0.063_854_172_8 * bb) ** 3;
	const s = (ll - 0.089_484_177_5 * aa - 1.291_485_548 * bb) ** 3;

	// Assume sRGB
	const r = srgbNonlinearTransform(4.076_741_662_1 * l - 3.307_711_591_3 * m + 0.230_969_929_2 * s);
	const g = srgbNonlinearTransform(-1.268_438_004_6 * l + 2.609_757_401_1 * m - 0.341_319_396_5 * s);
	const b = srgbNonlinearTransform(-0.004_196_086_3 * l - 0.703_418_614_7 * m + 1.707_614_701 * s);

	return [r * 255, g * 255, b * 255];
};

convert$1.oklch.oklab = function (oklch) {
	return convert$1.lch.lab(oklch);
};

convert$1.lab.xyz = function (lab) {
	const l = lab[0];
	const a = lab[1];
	const b = lab[2];
	let x;
	let y;
	let z;

	y = (l + 16) / 116;
	x = a / 500 + y;
	z = y - b / 200;

	const y2 = y ** 3;
	const x2 = x ** 3;
	const z2 = z ** 3;
	y = y2 > LAB_FT ? y2 : (y - 16 / 116) / 7.787;
	x = x2 > LAB_FT ? x2 : (x - 16 / 116) / 7.787;
	z = z2 > LAB_FT ? z2 : (z - 16 / 116) / 7.787;

	// Illuminant D65 XYZ Tristrimulus Values
	// https://en.wikipedia.org/wiki/CIE_1931_color_space
	x *= 95.047;
	y *= 100;
	z *= 108.883;

	return [x, y, z];
};

convert$1.lab.lch = function (lab) {
	const l = lab[0];
	const a = lab[1];
	const b = lab[2];
	let h;

	const hr = Math.atan2(b, a);
	h = hr * 360 / 2 / Math.PI;

	if (h < 0) {
		h += 360;
	}

	const c = Math.sqrt(a * a + b * b);

	return [l, c, h];
};

convert$1.lch.lab = function (lch) {
	const l = lch[0];
	const c = lch[1];
	const h = lch[2];

	const hr = h / 360 * 2 * Math.PI;
	const a = c * Math.cos(hr);
	const b = c * Math.sin(hr);

	return [l, a, b];
};

convert$1.rgb.ansi16 = function (args, saturation = null) {
	const [r, g, b] = args;
	let value = saturation === null ? convert$1.rgb.hsv(args)[2] : saturation; // Hsv -> ansi16 optimization

	value = Math.round(value / 50);

	if (value === 0) {
		return 30;
	}

	let ansi = 30
		/* eslint-disable no-bitwise */
		+ ((Math.round(b / 255) << 2)
		| (Math.round(g / 255) << 1)
		| Math.round(r / 255));
		/* eslint-enable no-bitwise */

	if (value === 2) {
		ansi += 60;
	}

	return ansi;
};

convert$1.hsv.ansi16 = function (args) {
	// Optimization here; we already know the value and don't need to get
	// it converted for us.
	return convert$1.rgb.ansi16(convert$1.hsv.rgb(args), args[2]);
};

convert$1.rgb.ansi256 = function (args) {
	const r = args[0];
	const g = args[1];
	const b = args[2];

	// We use the extended greyscale palette here, with the exception of
	// black and white. normal palette only has 4 greyscale shades.
	// eslint-disable-next-line no-bitwise
	if (r >> 4 === g >> 4 && g >> 4 === b >> 4) {
		if (r < 8) {
			return 16;
		}

		if (r > 248) {
			return 231;
		}

		return Math.round(((r - 8) / 247) * 24) + 232;
	}

	const ansi = 16
		+ (36 * Math.round(r / 255 * 5))
		+ (6 * Math.round(g / 255 * 5))
		+ Math.round(b / 255 * 5);

	return ansi;
};

convert$1.ansi16.rgb = function (args) {
	args = args[0];

	let color = args % 10;

	// Handle greyscale
	if (color === 0 || color === 7) {
		if (args > 50) {
			color += 3.5;
		}

		color = color / 10.5 * 255;

		return [color, color, color];
	}

	const mult = (Math.trunc(args > 50) + 1) * 0.5;
	/* eslint-disable no-bitwise */
	const r = ((color & 1) * mult) * 255;
	const g = (((color >> 1) & 1) * mult) * 255;
	const b = (((color >> 2) & 1) * mult) * 255;
	/* eslint-enable no-bitwise */

	return [r, g, b];
};

convert$1.ansi256.rgb = function (args) {
	args = args[0];

	// Handle greyscale
	if (args >= 232) {
		const c = (args - 232) * 10 + 8;
		return [c, c, c];
	}

	args -= 16;

	let rem;
	const r = Math.floor(args / 36) / 5 * 255;
	const g = Math.floor((rem = args % 36) / 6) / 5 * 255;
	const b = (rem % 6) / 5 * 255;

	return [r, g, b];
};

convert$1.rgb.hex = function (args) {
	/* eslint-disable no-bitwise */
	const integer = ((Math.round(args[0]) & 0xFF) << 16)
		+ ((Math.round(args[1]) & 0xFF) << 8)
		+ (Math.round(args[2]) & 0xFF);
	/* eslint-enable no-bitwise */

	const string = integer.toString(16).toUpperCase();
	return '000000'.slice(string.length) + string;
};

convert$1.hex.rgb = function (args) {
	const match = args.toString(16).match(/[a-f\d]{6}|[a-f\d]{3}/i);
	if (!match) {
		return [0, 0, 0];
	}

	let colorString = match[0];

	if (match[0].length === 3) {
		colorString = [...colorString].map(char => char + char).join('');
	}

	const integer = Number.parseInt(colorString, 16);
	/* eslint-disable no-bitwise */
	const r = (integer >> 16) & 0xFF;
	const g = (integer >> 8) & 0xFF;
	const b = integer & 0xFF;
	/* eslint-enable no-bitwise */

	return [r, g, b];
};

convert$1.rgb.hcg = function (rgb) {
	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;
	const max = Math.max(Math.max(r, g), b);
	const min = Math.min(Math.min(r, g), b);
	const chroma = (max - min);
	let hue;

	const grayscale = chroma < 1 ? min / (1 - chroma) : 0;

	if (chroma <= 0) {
		hue = 0;
	} else if (max === r) {
		hue = ((g - b) / chroma) % 6;
	} else if (max === g) {
		hue = 2 + (b - r) / chroma;
	} else {
		hue = 4 + (r - g) / chroma;
	}

	hue /= 6;
	hue %= 1;

	return [hue * 360, chroma * 100, grayscale * 100];
};

convert$1.hsl.hcg = function (hsl) {
	const s = hsl[1] / 100;
	const l = hsl[2] / 100;

	const c = l < 0.5 ? (2 * s * l) : (2 * s * (1 - l));

	let f = 0;
	if (c < 1) {
		f = (l - 0.5 * c) / (1 - c);
	}

	return [hsl[0], c * 100, f * 100];
};

convert$1.hsv.hcg = function (hsv) {
	const s = hsv[1] / 100;
	const v = hsv[2] / 100;

	const c = s * v;
	let f = 0;

	if (c < 1) {
		f = (v - c) / (1 - c);
	}

	return [hsv[0], c * 100, f * 100];
};

convert$1.hcg.rgb = function (hcg) {
	const h = hcg[0] / 360;
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;

	if (c === 0) {
		return [g * 255, g * 255, g * 255];
	}

	const pure = [0, 0, 0];
	const hi = (h % 1) * 6;
	const v = hi % 1;
	const w = 1 - v;
	let mg = 0;

	/* eslint-disable max-statements-per-line */
	switch (Math.floor(hi)) {
		case 0: {
			pure[0] = 1; pure[1] = v; pure[2] = 0; break;
		}

		case 1: {
			pure[0] = w; pure[1] = 1; pure[2] = 0; break;
		}

		case 2: {
			pure[0] = 0; pure[1] = 1; pure[2] = v; break;
		}

		case 3: {
			pure[0] = 0; pure[1] = w; pure[2] = 1; break;
		}

		case 4: {
			pure[0] = v; pure[1] = 0; pure[2] = 1; break;
		}

		default: {
			pure[0] = 1; pure[1] = 0; pure[2] = w;
		}
	}
	/* eslint-enable max-statements-per-line */

	mg = (1 - c) * g;

	return [
		(c * pure[0] + mg) * 255,
		(c * pure[1] + mg) * 255,
		(c * pure[2] + mg) * 255,
	];
};

convert$1.hcg.hsv = function (hcg) {
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;

	const v = c + g * (1 - c);
	let f = 0;

	if (v > 0) {
		f = c / v;
	}

	return [hcg[0], f * 100, v * 100];
};

convert$1.hcg.hsl = function (hcg) {
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;

	const l = g * (1 - c) + 0.5 * c;
	let s = 0;

	if (l > 0 && l < 0.5) {
		s = c / (2 * l);
	} else if (l >= 0.5 && l < 1) {
		s = c / (2 * (1 - l));
	}

	return [hcg[0], s * 100, l * 100];
};

convert$1.hcg.hwb = function (hcg) {
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;
	const v = c + g * (1 - c);
	return [hcg[0], (v - c) * 100, (1 - v) * 100];
};

convert$1.hwb.hcg = function (hwb) {
	const w = hwb[1] / 100;
	const b = hwb[2] / 100;
	const v = 1 - b;
	const c = v - w;
	let g = 0;

	if (c < 1) {
		g = (v - c) / (1 - c);
	}

	return [hwb[0], c * 100, g * 100];
};

convert$1.apple.rgb = function (apple) {
	return [(apple[0] / 65_535) * 255, (apple[1] / 65_535) * 255, (apple[2] / 65_535) * 255];
};

convert$1.rgb.apple = function (rgb) {
	return [(rgb[0] / 255) * 65_535, (rgb[1] / 255) * 65_535, (rgb[2] / 255) * 65_535];
};

convert$1.gray.rgb = function (args) {
	return [args[0] / 100 * 255, args[0] / 100 * 255, args[0] / 100 * 255];
};

convert$1.gray.hsl = function (args) {
	return [0, 0, args[0]];
};

convert$1.gray.hsv = convert$1.gray.hsl;

convert$1.gray.hwb = function (gray) {
	return [0, 100, gray[0]];
};

convert$1.gray.cmyk = function (gray) {
	return [0, 0, 0, gray[0]];
};

convert$1.gray.lab = function (gray) {
	return [gray[0], 0, 0];
};

convert$1.gray.hex = function (gray) {
	/* eslint-disable no-bitwise */
	const value = Math.round(gray[0] / 100 * 255) & 0xFF;
	const integer = (value << 16) + (value << 8) + value;
	/* eslint-enable no-bitwise */

	const string = integer.toString(16).toUpperCase();
	return '000000'.slice(string.length) + string;
};

convert$1.rgb.gray = function (rgb) {
	const value = (rgb[0] + rgb[1] + rgb[2]) / 3;
	return [value / 255 * 100];
};

/*
	This function routes a model to all other models.

	all functions that are routed have a property `.conversion` attached
	to the returned synthetic function. This property is an array
	of strings, each with the steps in between the 'from' and 'to'
	color models (inclusive).

	conversions that are not possible simply are not included.
*/

function buildGraph() {
	const graph = {};
	// https://jsperf.com/object-keys-vs-for-in-with-closure/3
	const models = Object.keys(convert$1);

	for (let {length} = models, i = 0; i < length; i++) {
		graph[models[i]] = {
			// http://jsperf.com/1-vs-infinity
			// micro-opt, but this is simple.
			distance: -1,
			parent: null,
		};
	}

	return graph;
}

// https://en.wikipedia.org/wiki/Breadth-first_search
function deriveBFS(fromModel) {
	const graph = buildGraph();
	const queue = [fromModel]; // Unshift -> queue -> pop

	graph[fromModel].distance = 0;

	while (queue.length > 0) {
		const current = queue.pop();
		const adjacents = Object.keys(convert$1[current]);

		for (let {length} = adjacents, i = 0; i < length; i++) {
			const adjacent = adjacents[i];
			const node = graph[adjacent];

			if (node.distance === -1) {
				node.distance = graph[current].distance + 1;
				node.parent = current;
				queue.unshift(adjacent);
			}
		}
	}

	return graph;
}

function link(from, to) {
	return function (args) {
		return to(from(args));
	};
}

function wrapConversion(toModel, graph) {
	const path = [graph[toModel].parent, toModel];
	let fn = convert$1[graph[toModel].parent][toModel];

	let cur = graph[toModel].parent;
	while (graph[cur].parent) {
		path.unshift(graph[cur].parent);
		fn = link(convert$1[graph[cur].parent][cur], fn);
		cur = graph[cur].parent;
	}

	fn.conversion = path;
	return fn;
}

function route(fromModel) {
	const graph = deriveBFS(fromModel);
	const conversion = {};

	const models = Object.keys(graph);
	for (let {length} = models, i = 0; i < length; i++) {
		const toModel = models[i];
		const node = graph[toModel];

		if (node.parent === null) {
			// No possible conversion, or this node is the source model.
			continue;
		}

		conversion[toModel] = wrapConversion(toModel, graph);
	}

	return conversion;
}

const convert = {};

const models = Object.keys(convert$1);

function wrapRaw(fn) {
	const wrappedFn = function (...args) {
		const arg0 = args[0];
		if (arg0 === undefined || arg0 === null) {
			return arg0;
		}

		if (arg0.length > 1) {
			args = arg0;
		}

		return fn(args);
	};

	// Preserve .conversion property if there is one
	if ('conversion' in fn) {
		wrappedFn.conversion = fn.conversion;
	}

	return wrappedFn;
}

function wrapRounded(fn) {
	const wrappedFn = function (...args) {
		const arg0 = args[0];

		if (arg0 === undefined || arg0 === null) {
			return arg0;
		}

		if (arg0.length > 1) {
			args = arg0;
		}

		const result = fn(args);

		// We're assuming the result is an array here.
		// see notice in conversions.js; don't use box types
		// in conversion functions.
		if (typeof result === 'object') {
			for (let {length} = result, i = 0; i < length; i++) {
				result[i] = Math.round(result[i]);
			}
		}

		return result;
	};

	// Preserve .conversion property if there is one
	if ('conversion' in fn) {
		wrappedFn.conversion = fn.conversion;
	}

	return wrappedFn;
}

for (const fromModel of models) {
	convert[fromModel] = {};

	Object.defineProperty(convert[fromModel], 'channels', {value: convert$1[fromModel].channels});
	Object.defineProperty(convert[fromModel], 'labels', {value: convert$1[fromModel].labels});

	const routes = route(fromModel);
	const routeModels = Object.keys(routes);

	for (const toModel of routeModels) {
		const fn = routes[toModel];

		convert[fromModel][toModel] = wrapRounded(fn);
		convert[fromModel][toModel].raw = wrapRaw(fn);
	}
}

const skippedModels = [
	// To be honest, I don't really feel like keyword belongs in color convert, but eh.
	'keyword',

	// Gray conflicts with some method names, and has its own method defined.
	'gray',

	// Shouldn't really be in color-convert either...
	'hex',
];

const hashedModelKeys = {};
for (const model of Object.keys(convert)) {
	hashedModelKeys[[...convert[model].labels].sort().join('')] = model;
}

const limiters = {};

function Color$1(object, model) {
	if (!(this instanceof Color$1)) {
		return new Color$1(object, model);
	}

	if (model && model in skippedModels) {
		model = null;
	}

	if (model && !(model in convert)) {
		throw new Error('Unknown model: ' + model);
	}

	let i;
	let channels;

	if (object == null) { // eslint-disable-line no-eq-null,eqeqeq
		this.model = 'rgb';
		this.color = [0, 0, 0];
		this.valpha = 1;
	} else if (object instanceof Color$1) {
		this.model = object.model;
		this.color = [...object.color];
		this.valpha = object.valpha;
	} else if (typeof object === 'string') {
		const result = cs.get(object);
		if (result === null) {
			throw new Error('Unable to parse color from string: ' + object);
		}

		this.model = result.model;
		channels = convert[this.model].channels;
		this.color = result.value.slice(0, channels);
		this.valpha = typeof result.value[channels] === 'number' ? result.value[channels] : 1;
	} else if (object.length > 0) {
		this.model = model || 'rgb';
		channels = convert[this.model].channels;
		const newArray = Array.prototype.slice.call(object, 0, channels);
		this.color = zeroArray(newArray, channels);
		this.valpha = typeof object[channels] === 'number' ? object[channels] : 1;
	} else if (typeof object === 'number') {
		// This is always RGB - can be converted later on.
		this.model = 'rgb';
		this.color = [
			(object >> 16) & 0xFF,
			(object >> 8) & 0xFF,
			object & 0xFF,
		];
		this.valpha = 1;
	} else {
		this.valpha = 1;

		const keys = Object.keys(object);
		if ('alpha' in object) {
			keys.splice(keys.indexOf('alpha'), 1);
			this.valpha = typeof object.alpha === 'number' ? object.alpha : 0;
		}

		const hashedKeys = keys.sort().join('');
		if (!(hashedKeys in hashedModelKeys)) {
			throw new Error('Unable to parse color from object: ' + JSON.stringify(object));
		}

		this.model = hashedModelKeys[hashedKeys];

		const {labels} = convert[this.model];
		const color = [];
		for (i = 0; i < labels.length; i++) {
			color.push(object[labels[i]]);
		}

		this.color = zeroArray(color);
	}

	// Perform limitations (clamping, etc.)
	if (limiters[this.model]) {
		channels = convert[this.model].channels;
		for (i = 0; i < channels; i++) {
			const limit = limiters[this.model][i];
			if (limit) {
				this.color[i] = limit(this.color[i]);
			}
		}
	}

	this.valpha = Math.max(0, Math.min(1, this.valpha));

	if (Object.freeze) {
		Object.freeze(this);
	}
}

Color$1.prototype = {
	toString() {
		return this.string();
	},

	toJSON() {
		return this[this.model]();
	},

	string(places) {
		let self = this.model in cs.to ? this : this.rgb();
		self = self.round(typeof places === 'number' ? places : 1);
		const arguments_ = self.valpha === 1 ? self.color : [...self.color, this.valpha];
		return cs.to[self.model](...arguments_);
	},

	percentString(places) {
		const self = this.rgb().round(typeof places === 'number' ? places : 1);
		const arguments_ = self.valpha === 1 ? self.color : [...self.color, this.valpha];
		return cs.to.rgb.percent(...arguments_);
	},

	array() {
		return this.valpha === 1 ? [...this.color] : [...this.color, this.valpha];
	},

	object() {
		const result = {};
		const {channels} = convert[this.model];
		const {labels} = convert[this.model];

		for (let i = 0; i < channels; i++) {
			result[labels[i]] = this.color[i];
		}

		if (this.valpha !== 1) {
			result.alpha = this.valpha;
		}

		return result;
	},

	unitArray() {
		const rgb = this.rgb().color;
		rgb[0] /= 255;
		rgb[1] /= 255;
		rgb[2] /= 255;

		if (this.valpha !== 1) {
			rgb.push(this.valpha);
		}

		return rgb;
	},

	unitObject() {
		const rgb = this.rgb().object();
		rgb.r /= 255;
		rgb.g /= 255;
		rgb.b /= 255;

		if (this.valpha !== 1) {
			rgb.alpha = this.valpha;
		}

		return rgb;
	},

	round(places) {
		places = Math.max(places || 0, 0);
		return new Color$1([...this.color.map(roundToPlace(places)), this.valpha], this.model);
	},

	alpha(value) {
		if (value !== undefined) {
			return new Color$1([...this.color, Math.max(0, Math.min(1, value))], this.model);
		}

		return this.valpha;
	},

	// Rgb
	red: getset('rgb', 0, maxfn(255)),
	green: getset('rgb', 1, maxfn(255)),
	blue: getset('rgb', 2, maxfn(255)),

	hue: getset(['hsl', 'hsv', 'hsl', 'hwb', 'hcg'], 0, value => ((value % 360) + 360) % 360),

	saturationl: getset('hsl', 1, maxfn(100)),
	lightness: getset('hsl', 2, maxfn(100)),

	saturationv: getset('hsv', 1, maxfn(100)),
	value: getset('hsv', 2, maxfn(100)),

	chroma: getset('hcg', 1, maxfn(100)),
	gray: getset('hcg', 2, maxfn(100)),

	white: getset('hwb', 1, maxfn(100)),
	wblack: getset('hwb', 2, maxfn(100)),

	cyan: getset('cmyk', 0, maxfn(100)),
	magenta: getset('cmyk', 1, maxfn(100)),
	yellow: getset('cmyk', 2, maxfn(100)),
	black: getset('cmyk', 3, maxfn(100)),

	x: getset('xyz', 0, maxfn(95.047)),
	y: getset('xyz', 1, maxfn(100)),
	z: getset('xyz', 2, maxfn(108.833)),

	l: getset('lab', 0, maxfn(100)),
	a: getset('lab', 1),
	b: getset('lab', 2),

	keyword(value) {
		if (value !== undefined) {
			return new Color$1(value);
		}

		return convert[this.model].keyword(this.color);
	},

	hex(value) {
		if (value !== undefined) {
			return new Color$1(value);
		}

		return cs.to.hex(...this.rgb().round().color);
	},

	hexa(value) {
		if (value !== undefined) {
			return new Color$1(value);
		}

		const rgbArray = this.rgb().round().color;

		let alphaHex = Math.round(this.valpha * 255).toString(16).toUpperCase();
		if (alphaHex.length === 1) {
			alphaHex = '0' + alphaHex;
		}

		return cs.to.hex(...rgbArray) + alphaHex;
	},

	rgbNumber() {
		const rgb = this.rgb().color;
		return ((rgb[0] & 0xFF) << 16) | ((rgb[1] & 0xFF) << 8) | (rgb[2] & 0xFF);
	},

	luminosity() {
		// http://www.w3.org/TR/WCAG20/#relativeluminancedef
		const rgb = this.rgb().color;

		const lum = [];
		for (const [i, element] of rgb.entries()) {
			const chan = element / 255;
			lum[i] = (chan <= 0.04045) ? chan / 12.92 : ((chan + 0.055) / 1.055) ** 2.4;
		}

		return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
	},

	contrast(color2) {
		// http://www.w3.org/TR/WCAG20/#contrast-ratiodef
		const lum1 = this.luminosity();
		const lum2 = color2.luminosity();

		if (lum1 > lum2) {
			return (lum1 + 0.05) / (lum2 + 0.05);
		}

		return (lum2 + 0.05) / (lum1 + 0.05);
	},

	level(color2) {
		// https://www.w3.org/TR/WCAG/#contrast-enhanced
		const contrastRatio = this.contrast(color2);
		if (contrastRatio >= 7) {
			return 'AAA';
		}

		return (contrastRatio >= 4.5) ? 'AA' : '';
	},

	isDark() {
		// YIQ equation from http://24ways.org/2010/calculating-color-contrast
		const rgb = this.rgb().color;
		const yiq = (rgb[0] * 2126 + rgb[1] * 7152 + rgb[2] * 722) / 10000;
		return yiq < 128;
	},

	isLight() {
		return !this.isDark();
	},

	negate() {
		const rgb = this.rgb();
		for (let i = 0; i < 3; i++) {
			rgb.color[i] = 255 - rgb.color[i];
		}

		return rgb;
	},

	lighten(ratio) {
		const hsl = this.hsl();
		hsl.color[2] += hsl.color[2] * ratio;
		return hsl;
	},

	darken(ratio) {
		const hsl = this.hsl();
		hsl.color[2] -= hsl.color[2] * ratio;
		return hsl;
	},

	saturate(ratio) {
		const hsl = this.hsl();
		hsl.color[1] += hsl.color[1] * ratio;
		return hsl;
	},

	desaturate(ratio) {
		const hsl = this.hsl();
		hsl.color[1] -= hsl.color[1] * ratio;
		return hsl;
	},

	whiten(ratio) {
		const hwb = this.hwb();
		hwb.color[1] += hwb.color[1] * ratio;
		return hwb;
	},

	blacken(ratio) {
		const hwb = this.hwb();
		hwb.color[2] += hwb.color[2] * ratio;
		return hwb;
	},

	grayscale() {
		// http://en.wikipedia.org/wiki/Grayscale#Converting_colour_to_grayscale
		const rgb = this.rgb().color;
		const value = rgb[0] * 0.3 + rgb[1] * 0.59 + rgb[2] * 0.11;
		return Color$1.rgb(value, value, value);
	},

	fade(ratio) {
		return this.alpha(this.valpha - (this.valpha * ratio));
	},

	opaquer(ratio) {
		return this.alpha(this.valpha + (this.valpha * ratio));
	},

	rotate(degrees) {
		const hsl = this.hsl();
		let hue = hsl.color[0];
		hue = (hue + degrees) % 360;
		hue = hue < 0 ? 360 + hue : hue;
		hsl.color[0] = hue;
		return hsl;
	},

	mix(mixinColor, weight) {
		// Ported from sass implementation in C
		// https://github.com/sass/libsass/blob/0e6b4a2850092356aa3ece07c6b249f0221caced/functions.cpp#L209
		if (!mixinColor || !mixinColor.rgb) {
			throw new Error('Argument to "mix" was not a Color instance, but rather an instance of ' + typeof mixinColor);
		}

		const color1 = mixinColor.rgb();
		const color2 = this.rgb();
		const p = weight === undefined ? 0.5 : weight;

		const w = 2 * p - 1;
		const a = color1.alpha() - color2.alpha();

		const w1 = (((w * a === -1) ? w : (w + a) / (1 + w * a)) + 1) / 2;
		const w2 = 1 - w1;

		return Color$1.rgb(
			w1 * color1.red() + w2 * color2.red(),
			w1 * color1.green() + w2 * color2.green(),
			w1 * color1.blue() + w2 * color2.blue(),
			color1.alpha() * p + color2.alpha() * (1 - p));
	},
};

// Model conversion methods and static constructors
for (const model of Object.keys(convert)) {
	if (skippedModels.includes(model)) {
		continue;
	}

	const {channels} = convert[model];

	// Conversion methods
	Color$1.prototype[model] = function (...arguments_) {
		if (this.model === model) {
			return new Color$1(this);
		}

		if (arguments_.length > 0) {
			return new Color$1(arguments_, model);
		}

		return new Color$1([...assertArray(convert[this.model][model].raw(this.color)), this.valpha], model);
	};

	// 'static' construction methods
	Color$1[model] = function (...arguments_) {
		let color = arguments_[0];
		if (typeof color === 'number') {
			color = zeroArray(arguments_, channels);
		}

		return new Color$1(color, model);
	};
}

function roundTo(number, places) {
	return Number(number.toFixed(places));
}

function roundToPlace(places) {
	return function (number) {
		return roundTo(number, places);
	};
}

function getset(model, channel, modifier) {
	model = Array.isArray(model) ? model : [model];

	for (const m of model) {
		(limiters[m] ||= [])[channel] = modifier;
	}

	model = model[0];

	return function (value) {
		let result;

		if (value !== undefined) {
			if (modifier) {
				value = modifier(value);
			}

			result = this[model]();
			result.color[channel] = value;
			return result;
		}

		result = this[model]().color[channel];
		if (modifier) {
			result = modifier(result);
		}

		return result;
	};
}

function maxfn(max) {
	return function (v) {
		return Math.max(0, Math.min(max, v));
	};
}

function assertArray(value) {
	return Array.isArray(value) ? value : [value];
}

function zeroArray(array, length) {
	for (let i = 0; i < length; i++) {
		if (typeof array[i] !== 'number') {
			array[i] = 0;
		}
	}

	return array;
}

// TODO: Replace this function with constants
/** Generate variants of a given colour. */
function colorToGraphicsOpts(color, defaultAlpha = 1, isDarkMode = false) {
    const cObj = new Color$1(color);
    return {
        // TODO: Generate unselected colors
        selected: {
            tint: cObj.rgbNumber(),
            alpha: defaultAlpha,
        },
        hover: {
            tint: isDarkMode ? cObj.lighten(0.5).rgbNumber() : cObj.darken(0.5).rgbNumber(),
            alpha: defaultAlpha,
        },
        unselected: {
            tint: color,
            alpha: 0.3,
        },
        selectionNeutral: {
            tint: color,
            alpha: defaultAlpha,
        },
    };
}
function parseColorString(colorStr) {
    const color = new Color$1(colorStr);
    return { color: color.rgbNumber(), alpha: color.alpha() };
}
function colorToGraphicsOpts2(color, selectColor, defaultAlpha = 1) {
    return {
        // TODO: Generate unselected colors
        selected: {
            tint: selectColor,
            alpha: defaultAlpha,
        },
        hover: {
            tint: new Color$1(selectColor).darken(0.5).rgbNumber(),
            alpha: defaultAlpha,
        },
        unselected: {
            tint: color,
            alpha: 0.5,
        },
        selectionNeutral: {
            tint: color,
            alpha: defaultAlpha,
        },
    };
}
function colorToGraphicsOpts3(color, selectColor, hoverColor, defaultAlpha = 1) {
    return {
        // TODO: Generate unselected colors
        selected: {
            tint: selectColor,
            alpha: defaultAlpha,
        },
        hover: {
            tint: hoverColor,
            alpha: defaultAlpha,
        },
        unselected: {
            tint: color,
            alpha: 0.5,
        },
        selectionNeutral: {
            tint: color,
            alpha: defaultAlpha,
        },
    };
}
function getContrastingColor(color) {
    const luminance = new Color$1(color).luminosity();
    const contrastWithBlack = (luminance + 0.05) / 0.05;
    const contrastWithWhite = 1.05 / (luminance + 0.05);
    return contrastWithWhite >= contrastWithBlack ? 0xffffff : 0x000000;
}
function getContrastingGraphicsOpts(opts) {
    return {
        selected: {
            tint: getContrastingColor(opts.selected.tint),
            alpha: opts.selected.alpha,
        },
        hover: {
            tint: getContrastingColor(opts.hover.tint),
            alpha: opts.hover.alpha,
        },
        unselected: {
            tint: getContrastingColor(opts.unselected.tint),
            alpha: opts.unselected.alpha,
        },
        selectionNeutral: {
            tint: getContrastingColor(opts.selectionNeutral.tint),
            alpha: opts.selectionNeutral.alpha,
        },
    };
}

var DesignModelsExports = requireDesignModels();

function getDefaultColors() {
    return {
        COLOR_FRONT: colorToGraphicsOpts(0xcd4341, 0.9),
        COLOR_COPPER_1: colorToGraphicsOpts2(0xffe74c, 0xffe74c, 0.8),
        COLOR_COPPER_2: colorToGraphicsOpts2(0x91d178, 0x91d178, 0.8),
        COLOR_COPPER_3: colorToGraphicsOpts2(0xffdda1, 0xffdda1, 0.8),
        COLOR_COPPER_4: colorToGraphicsOpts2(0x5fad56, 0x5fad56, 0.8),
        COLOR_COPPER_5: colorToGraphicsOpts2(0x00bfb2, 0x00bfb2, 0.8),
        COLOR_COPPER_6: colorToGraphicsOpts2(0x65def1, 0x65def1, 0.8),
        COLOR_COPPER_7: colorToGraphicsOpts2(0xc55ea3, 0xc55ea3, 0.8),
        COLOR_COPPER_8: colorToGraphicsOpts2(0xf56416, 0xf56416, 0.8),
        COLOR_BOTTOM: colorToGraphicsOpts(0x5fa9e6, 0.9),
        COLOR_BORDERS: colorToGraphicsOpts(0xb3b3b3),
        COLOR_COPPER: colorToGraphicsOpts(0xc83434, 0.9),
        COLOR_VIA: colorToGraphicsOpts(0xa5a010),
        COLOR_VIA_MIDDLE: colorToGraphicsOpts(0xffffff),
        COLOR_ZONE: colorToGraphicsOpts(0xc43e49),
        COLOR_COMPONENT: colorToGraphicsOpts(0x10a5a0),
        COLOR_COMPONENT_OUTLINE: colorToGraphicsOpts(0x0c9590),
        COLOR_SILKSCREEN: colorToGraphicsOpts(0xb3b3b3),
        COLOR_SELECTED_ELEMENTS: colorToGraphicsOpts(0xa86932),
        COLOR_SCH_NET: colorToGraphicsOpts(0x009600, 1),
        COLOR_SCH_BG: colorToGraphicsOpts(0xffffc2, 1),
        COLOR_SCH_SYMBOL: colorToGraphicsOpts(0x840000, 1),
        COLOR_SCH_BUS: colorToGraphicsOpts(0x000084, 1),
        COLOR_SCH_PINNAME: colorToGraphicsOpts(0x006464, 1),
        COLOR_SCH_IMAGE: {
            selected: { tint: 0xffffff, alpha: 1 },
            hover: { tint: 0xffffff, alpha: 1 },
            unselected: { tint: 0xffffff, alpha: 1 },
            selectionNeutral: { tint: 0xffffff, alpha: 1 },
        },
        OVERLAY_LAYER: colorToGraphicsOpts(0x7a7a7a, 0.8),
        USER_SELECTION_LAYER: colorToGraphicsOpts(0xa86932),
        BACKGROUND_LAYER: colorToGraphicsOpts(0x252525, 1),
        OVERLAY_ANNOTATIONS: ConstantsExports.OVERLAY_ANNOTATIONS_COLOR,
    };
}

var Constants$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getDefaultColors: getDefaultColors
});

class DefaultLayerColorProvider {
    enableChangeWatchers;
    static MAX_LISTENERS = 1000;
    cache = new Map();
    contrastCache = new Map();
    defaults = getDefaultColors();
    layers = new Map();
    extraCopperLayers = 0;
    constructor(layers, enableChangeWatchers = false) {
        this.enableChangeWatchers = enableChangeWatchers;
        for (const layer of layers) {
            this.layers.set(layer.id, layer);
        }
    }
    getColorForLayer(id) {
        if (this.cache.has(id)) {
            return this.cache.get(id);
        }
        const opts = this.maybeAddChangeWatcher(this.computeOptsForLayer(id));
        this.cache.set(id, opts);
        return opts;
    }
    getContrastingColorForLayer(id) {
        if (this.contrastCache.has(id)) {
            return this.contrastCache.get(id);
        }
        const opts = this.maybeAddChangeWatcher(getContrastingGraphicsOpts(this.getColorForLayer(id)));
        this.contrastCache.set(id, opts);
        return opts;
    }
    maybeAddChangeWatcher(opts) {
        if (this.enableChangeWatchers) {
            const changeWatcher = new EventEmitter();
            opts = {
                ...opts,
                changeWatcher,
            };
            changeWatcher.setMaxListeners(DefaultLayerColorProvider.MAX_LISTENERS);
        }
        return opts;
    }
    generateToolCacheKey(tool, opt) {
        return `${tool}~${opt}`;
    }
    getColorForTool(tool, opt = 'default') {
        const key = this.generateToolCacheKey(tool, opt);
        if (this.cache.has(key)) {
            return this.cache.get(key);
        }
        const opts = this.maybeAddChangeWatcher({ ...this.defaults.OVERLAY_LAYER });
        this.cache.set(key, opts);
        return opts;
    }
    /**
     * Sets the colour for the layer. Must be called before the colour is used anywhere.
     *
     * If a `changeWatcher` is provided in `opts`, this method will emit a `changed` event whenever it's called. That way,
     * you can listen for when a new layer colour has been set and update the UI accordingly.
     */
    setLayerColor(id, opts) {
        if (this.cache.has(id)) {
            if (this.enableChangeWatchers) {
                const current = this.cache.get(id);
                const changeWatcher = current.changeWatcher;
                lodashExports.merge(current, opts);
                current.changeWatcher = changeWatcher;
                changeWatcher?.emit('changed', current);
                this.cache.set(id, current);
                this.updateContrastingLayerColor(id, current);
            }
            else {
                console.warn('Updating layer color already in use without change watchers on');
                this.cache.set(id, opts);
                this.updateContrastingLayerColor(id, opts);
            }
        }
        else {
            const layerOpts = this.maybeAddChangeWatcher(opts);
            this.cache.set(id, layerOpts);
            this.updateContrastingLayerColor(id, layerOpts);
        }
    }
    updateContrastingLayerColor(id, layerOpts) {
        if (!this.contrastCache.has(id)) {
            return;
        }
        const opts = getContrastingGraphicsOpts(layerOpts);
        if (this.enableChangeWatchers) {
            const current = this.contrastCache.get(id);
            const changeWatcher = current.changeWatcher;
            lodashExports.merge(current, opts);
            current.changeWatcher = changeWatcher;
            changeWatcher?.emit('changed', current);
            this.contrastCache.set(id, current);
        }
        else {
            this.contrastCache.set(id, opts);
        }
    }
    getToolForLayer(id) {
        let opt = 'default';
        switch (id) {
            case ConstantsExports.SelectedElementsLayer:
                opt = 'selectedElements';
                break;
            case ConstantsExports.OverlayLayer:
                opt = 'overlay';
                break;
            case ConstantsExports.UserSelectionLayer:
                opt = 'userSelection';
                break;
            case ConstantsExports.BackgroundLayer:
                opt = 'background';
                break;
        }
        return { tool: 'default', opt };
    }
    computeOptsForLayer(id) {
        const layer = this.layers.get(id);
        if (!layer) {
            switch (id) {
                case ConstantsExports.SelectedElementsLayer:
                    return this.defaults.COLOR_SELECTED_ELEMENTS;
                case ConstantsExports.OverlayLayer:
                    return this.defaults.OVERLAY_LAYER;
                case ConstantsExports.UserSelectionLayer:
                    return this.defaults.USER_SELECTION_LAYER;
                case ConstantsExports.BackgroundLayer:
                    return this.defaults.BACKGROUND_LAYER;
            }
            return this.defaults.COLOR_ZONE;
        }
        switch (layer.type) {
            case DesignModelsExports.PcbLayerType.F:
                return this.defaults.COLOR_FRONT;
            case DesignModelsExports.PcbLayerType.B:
                return this.defaults.COLOR_BOTTOM;
            case DesignModelsExports.PcbLayerType.Inner: {
                const index = `COLOR_COPPER_${++this.extraCopperLayers}`;
                return this.defaults[index] || this.defaults.COLOR_COPPER;
            }
            case DesignModelsExports.PcbLayerType.Edge:
                return this.defaults.COLOR_BORDERS;
            case DesignModelsExports.PcbLayerType.Other:
                return this.defaults.COLOR_COMPONENT_OUTLINE;
            case DesignModelsExports.PcbLayerType.SilkS:
                return this.defaults.COLOR_SILKSCREEN;
            case DesignModelsExports.SchLayerType.Background:
                return this.defaults.COLOR_SCH_BG;
            case DesignModelsExports.SchLayerType.Bus:
            case DesignModelsExports.SchLayerType.BusJunction:
                return this.defaults.COLOR_SCH_BUS;
            case DesignModelsExports.SchLayerType.Label:
            case DesignModelsExports.SchLayerType.Wire:
            case DesignModelsExports.SchLayerType.Junction:
                return this.defaults.COLOR_SCH_NET;
            case DesignModelsExports.SchLayerType.Pin:
            case DesignModelsExports.SchLayerType.PinNumber:
            case DesignModelsExports.SchLayerType.Symbol:
                return this.defaults.COLOR_SCH_SYMBOL;
            case DesignModelsExports.SchLayerType.PinName:
            case DesignModelsExports.SchLayerType.NameLabel:
            case DesignModelsExports.SchLayerType.ValueLabel:
                return this.defaults.COLOR_SCH_PINNAME;
            case DesignModelsExports.SchLayerType.Image:
                return this.defaults.COLOR_SCH_IMAGE;
        }
        return this.defaults.COLOR_ZONE;
    }
}

class InteractionState extends EventEmitter {
    layerSets;
    opts;
    colorProviders = new Map();
    currentSelectionArgs;
    currentHighlightArgs;
    constructor(layerSets, opts = {}) {
        super();
        this.layerSets = layerSets;
        this.opts = opts;
    }
    getCurrentSelection() {
        return this.currentSelectionArgs;
    }
    getCurrentHighlight() {
        return this.currentHighlightArgs;
    }
    getColorProviderForLayerSet(layerSet) {
        if (!this.colorProviders.has(layerSet)) {
            this.colorProviders.set(layerSet, new (this.opts.colorProviders?.defaultClass || DefaultLayerColorProvider)(this.layerSets[layerSet], !!this.opts.colorProviders?.enableChangeWatchers));
        }
        return this.colorProviders.get(layerSet);
    }
    selectNet(name, addToSelection = false) {
        this.selectElementsByProperty(DrawingProperties.Net, name, addToSelection);
    }
    selectElementsByProperty(property, value, addToSelection = false) {
        if (!this.currentSelectionArgs || !addToSelection) {
            this.currentSelectionArgs = new Map([[property, new Set([value])]]);
        }
        else if (!this.currentSelectionArgs.has(property)) {
            this.currentSelectionArgs.set(property, new Set([value]));
        }
        else if (!this.currentSelectionArgs.get(property).has(value)) {
            this.currentSelectionArgs.get(property).add(value);
        }
        this.emit('selectElementsByProperty', {
            allSelectionArgs: this.getCurrentSelection(),
            property,
            value,
            addToSelection,
        });
    }
    unSelectElementsByProperty(property, value) {
        if (this.currentSelectionArgs?.has(property)) {
            const prop = this.currentSelectionArgs.get(property);
            if (prop.has(value)) {
                prop.delete(value);
                if (prop.size === 0 && this.currentSelectionArgs.size === 1) {
                    this.currentSelectionArgs = undefined;
                    this.emit('clearSelection');
                }
                else {
                    this.emit('selectElementsByProperty', {
                        allSelectionArgs: this.getCurrentSelection(),
                        property,
                        value,
                        addToSelection: true,
                    });
                }
            }
        }
    }
    highlightElementsByProperty(property, value, addToHighlights = false) {
        if (!this.currentHighlightArgs || !addToHighlights) {
            this.currentHighlightArgs = new Map([[property, new Set([value])]]);
        }
        else if (!this.currentHighlightArgs.has(property)) {
            this.currentHighlightArgs.set(property, new Set([value]));
        }
        else if (!this.currentHighlightArgs.get(property).has(value)) {
            this.currentHighlightArgs.get(property).add(value);
        }
        this.emit('highlightElementsByProperty', {
            allSelectionArgs: this.getCurrentHighlight(),
            property,
            value,
            addToSelection: addToHighlights,
        });
    }
    unHighlightElementsByProperty(property, value) {
        if (this.currentHighlightArgs?.has(property)) {
            const prop = this.currentHighlightArgs.get(property);
            if (prop.has(value)) {
                prop.delete(value);
                if (prop.size === 0 && this.currentHighlightArgs.size === 1) {
                    this.currentHighlightArgs = undefined;
                    this.emit('clearHighlight');
                }
                else {
                    this.emit('highlightElementsByProperty', {
                        allSelectionArgs: this.getCurrentHighlight(),
                        property,
                        value,
                        addToSelection: true,
                    });
                }
            }
        }
    }
    clearSelection() {
        this.currentSelectionArgs = undefined;
        this.emit('clearSelection');
    }
    clearHighlight() {
        this.currentHighlightArgs = undefined;
        this.emit('clearHighlight');
    }
    on(eventName, listener) {
        return super.on(eventName, listener);
    }
}

/**
 * Animates property changes when they occur.
 *
 * Only numerical properties on the properties object are animated.
 * Non-numerical properties are updated at the end of the update.
 */
class AnimatedProperties {
    static DefaultGoalSeekTime = 250;
    goalSeekTime;
    setStaticPropsAtStart;
    currentProps;
    goalProps;
    referenceProps;
    seekStart;
    constructor(baseProps, args) {
        this.currentProps = baseProps;
        this.goalSeekTime = args ? args.goalSeekTimeMs : AnimatedProperties.DefaultGoalSeekTime;
        if (args?.setStaticPropsTime === 'start') {
            this.setStaticPropsAtStart = true;
        }
    }
    get current() {
        return this.currentProps;
    }
    get isSeeking() {
        return Boolean(this.goalProps);
    }
    tick() {
        this.calculateNextProps();
    }
    setStateImmedate(state) {
        this.currentProps = state;
        this.stopSeeking();
    }
    setGoalState(goal, setStaticPropsTime) {
        if (this.goalSeekTime < 1) {
            this.currentProps = goal;
            return;
        }
        if (setStaticPropsTime === 'start') {
            this.setStaticPropsAtStart = true;
        }
        else if (this.setStaticPropsAtStart) {
            delete this.setStaticPropsAtStart;
        }
        this.goalProps = { ...goal };
        this.referenceProps = { ...this.currentProps };
        this.seekStart = Date.now();
    }
    stopSeeking() {
        this.goalProps = undefined;
        this.referenceProps = undefined;
        this.seekStart = undefined;
        // delete this.goalProps;
        // delete this.referenceProps;
        // delete this.seekStart;
    }
    calculateNextProps() {
        if (!this.seekStart || !this.goalProps) {
            return;
        }
        const thisUpdate = Date.now();
        const timeDiff = thisUpdate - this.seekStart;
        const percentDistance = timeDiff / this.goalSeekTime;
        if (timeDiff >= this.goalSeekTime) {
            this.currentProps = { ...this.goalProps };
            this.stopSeeking();
            return;
        }
        for (const prop of Object.keys(this.goalProps)) {
            if (typeof this.goalProps[prop] !== 'number' || typeof this.currentProps[prop] !== 'number') {
                // Only number props get animated
                if (this.setStaticPropsAtStart) {
                    this.currentProps[prop] = this.goalProps[prop];
                }
                continue;
            }
            this.currentProps[prop] =
                this.referenceProps[prop] + (this.goalProps[prop] - this.referenceProps[prop]) * percentDistance;
        }
    }
}

class CommentOverlay extends DrawingElement {
    drawingManager;
    args;
    layer;
    graphicTags;
    static TextScaleFactor = ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
    static DefaultAnimateTimeMs = 150;
    state;
    tickEnabled;
    maskRect;
    containerRect;
    profilePics;
    textElements;
    stayShown;
    initialScale;
    oneShotEnabled;
    isDestroyed;
    controlledChildren;
    renderedContentsWidth = 0;
    renderedContentsHeight = 0;
    loader;
    profileResourceIndices = new Map();
    textureGraphics = new Map();
    loadCompleteHandler = null;
    loadCompleteHandlerBinding = null;
    loadErrorHandler = null;
    loadErrorHandlerBinding = null;
    loadedResources = 0;
    constructor(drawingManager, args, layer, graphicTags) {
        super(drawingManager, args, 'drawings', ObjectSelectionType.None);
        this.drawingManager = drawingManager;
        this.args = args;
        this.layer = layer;
        this.graphicTags = graphicTags;
        const factor = this.getWorldLength(1);
        this.initialScale = this.getCurrentScale();
        this.args = {
            textScaleFactor: CommentOverlay.TextScaleFactor,
            ...this.args,
            position: [this.args.position[0] * ConstantsExports.WORLD_SCALE, this.args.position[1] * ConstantsExports.WORLD_SCALE],
            fontSize: this.args.fontSize * factor,
            maxHeight: this.args.maxHeight * factor,
            maxWidth: this.args.maxWidth * factor,
            expanded: this.scaleStyling(this.args.expanded, factor),
            collapsed: this.scaleStyling(this.args.collapsed, factor),
            profilePictureArgs: {
                borderSize: this.args.profilePictureArgs.borderSize * factor,
                height: this.args.profilePictureArgs.height * factor,
                width: this.args.profilePictureArgs.width * factor,
                charStyleOpts: this.args.profilePictureArgs.charStyleOpts,
            },
        };
        this.on('hoverstatechanged', this.hoverStateChanged);
        this.drawingManager.viewport.on('zoomed', this.zoomed);
        this.loader = PIXI.Loader.shared;
    }
    destroy() {
        if (!this.isDestroyed) {
            this.isDestroyed = true;
            if (this.listeners['hoverstatechanged']) {
                this.removeListener('hoverstatechanged', this.hoverStateChanged);
            }
            this.drawingManager.viewport.removeListener('zoomed', this.zoomed);
            this.disableTick();
        }
        for (const graphic of this.textureGraphics.values()) {
            graphic.destroy();
        }
        this.textureGraphics.clear();
        if (this.loadCompleteHandlerBinding) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.loader.onLoad.detach(this.loadCompleteHandlerBinding);
            this.loadCompleteHandler = null;
        }
        if (this.loadErrorHandlerBinding) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.loader.onError.detach(this.loadErrorHandlerBinding);
            this.loadErrorHandler = null;
        }
        super.destroy();
    }
    get visible() {
        return Boolean(this.container?.visible);
    }
    set visible(value) {
        if (!this.container) {
            if (value) {
                this.render();
            }
        }
        else {
            this.container.visible = value;
        }
    }
    setInteractionEnabled(_enabled) {
        // Block this method
        return;
    }
    /**e
     * @param stayShown If true, this render will not auto hide until .hide() is called.
     */
    show(stayShown) {
        if (stayShown) {
            this.stayShown = true;
        }
        if (!this.state) {
            this.render();
        }
        this.state.setGoalState(this.getExpandedState(), 'start');
        this.enableTick();
    }
    hide() {
        if (this.stayShown) {
            delete this.stayShown;
        }
        if (!this.state) {
            return;
        }
        this.state.setGoalState(this.getCollapsedState(), 'start');
        this.enableTick();
    }
    addControlledChild(child) {
        child.setVisible(this.state.current.visible);
        if (child.container.parent) {
            child.container.parent.sortableChildren = true;
        }
        if (!this.controlledChildren) {
            this.controlledChildren = [child];
        }
        else {
            this.controlledChildren.push(child);
        }
    }
    render() {
        if (!this.state) {
            this.renderInitial();
        }
        const fullHeight = this.state.current.height + 2 * this.state.current.borderPadding;
        if (this.args.enableZoomScaling) {
            const currentScale = this.getCurrentScale();
            const applyScale = [this.initialScale[0] / currentScale[0], this.initialScale[1] / currentScale[1]];
            this.container.scale.set(applyScale[0], applyScale[1]);
            if (this.args.expandDirection === 'up') {
                this.container.pivot.set(0, fullHeight);
            }
        }
        this.container.zIndex = this.state.current.visible ? 1000 : 1;
        this.containerRect.clear();
        this.containerRect.beginFill(ConstantsExports.COLOR_UNFILLED);
        this.containerRect.drawRoundedRect(0, 0, this.state.current.width + 2 * this.state.current.borderPadding, fullHeight, this.state.current.borderRadius);
        if (this.args.expandDirection === 'up') {
            this.containerRect.drawRect(0, fullHeight - this.state.current.borderRadius, this.state.current.borderRadius, this.state.current.borderRadius);
        }
        else {
            this.containerRect.drawRect(0, 0, this.state.current.borderRadius, this.state.current.borderRadius);
        }
        this.maskRect.clear();
        this.maskRect.beginFill(ConstantsExports.COLOR_UNFILLED);
        this.maskRect.drawRect(this.state.current.borderPadding, this.state.current.borderPadding, this.state.current.width, this.state.current.height);
        this.profilePics.forEach(([sprite, bg], i) => {
            const x = this.state.current.borderPadding +
                (this.profilePics.length - i - 1) *
                    (this.args.profilePictureArgs.width + this.state.current.imageSpacing + this.state.current.borderPadding);
            const y = this.state.current.borderPadding;
            sprite.x = x + this.args.profilePictureArgs.width / 2;
            sprite.y = y + this.args.profilePictureArgs.height / 2;
            bg.x = x;
            bg.y = y;
            if (sprite.mask) {
                sprite.mask.x = x;
                sprite.mask.y = y;
            }
        });
        if (this.controlledChildren) {
            for (const child of this.controlledChildren) {
                child.container.zIndex = this.container.zIndex - 1;
                child.setVisible(this.state.current.visible);
            }
        }
    }
    renderInitial() {
        let unloadedResources = false;
        this.container.x = this.args.position[0];
        this.container.y = this.args.position[1];
        const settings = { tags: this.graphicTags };
        this.state = new AnimatedProperties(this.getCollapsedState(), {
            goalSeekTimeMs: CommentOverlay.DefaultAnimateTimeMs,
        });
        this.containerRect = new PIXI.Graphics();
        this.containerRect.beginFill(ConstantsExports.COLOR_UNFILLED);
        this.containerRect.drawRoundedRect(0, 0, this.state.current.width + this.state.current.borderPadding, this.state.current.height + this.state.current.borderPadding, this.state.current.borderRadius);
        this.containerRect.cursor = 'pointer';
        this.addGraphic(this.layer, this.containerRect, this.args.backgroundColor, settings);
        this.maskRect = new PIXI.Graphics();
        this.maskRect.beginFill(ConstantsExports.COLOR_UNFILLED);
        this.maskRect.drawRect(this.state.current.borderPadding, this.state.current.borderPadding, this.state.current.width - 2 * this.state.current.borderPadding, this.state.current.height - 2 * this.state.current.borderPadding);
        this.addGraphic(this.layer + 1, this.maskRect, this.args.backgroundColor, settings);
        this.profilePics = this.args.profilePictures.reverse().map((pic, i) => {
            if (this.profileResourceIndices.has(pic.username)) {
                this.profileResourceIndices.get(pic.username).push(i);
            }
            else {
                this.profileResourceIndices.set(pic.username, [i]);
            }
            unloadedResources = this.maybeLoadResource(pic) || unloadedResources;
            const x = this.state.current.borderPadding +
                i * (this.args.profilePictureArgs.width + this.state.current.imageSpacing + this.state.current.borderPadding);
            const y = this.state.current.borderPadding;
            const sprite = PIXI.Sprite.from(this.loadTextureForResource(pic.username));
            [sprite.width, sprite.height] = this.getTextureSizeScaled(sprite.texture);
            sprite.anchor.set(0.5, 0.5);
            sprite.x = x + sprite.width / 2;
            sprite.y = y + sprite.height / 2;
            sprite.cursor = 'pointer';
            const bg = new PIXI.Graphics();
            bg.x = x;
            bg.y = y;
            bg.beginFill(ConstantsExports.COLOR_UNFILLED);
            bg.drawEllipse((ConstantsExports.TEXTURIZED_ELEMENT_SCALE * this.args.profilePictureArgs.width) / 2, (ConstantsExports.TEXTURIZED_ELEMENT_SCALE * this.args.profilePictureArgs.height) / 2, ConstantsExports.TEXTURIZED_ELEMENT_SCALE * (this.args.profilePictureArgs.borderSize + this.args.profilePictureArgs.width / 2), ConstantsExports.TEXTURIZED_ELEMENT_SCALE * (this.args.profilePictureArgs.borderSize + this.args.profilePictureArgs.height / 2));
            bg.scale.set(1 / ConstantsExports.TEXTURIZED_ELEMENT_SCALE);
            bg.cursor = 'pointer';
            const mask = new PIXI.Graphics();
            mask.x = x;
            mask.y = y;
            mask.beginFill(ConstantsExports.COLOR_UNFILLED);
            mask.drawEllipse((ConstantsExports.TEXTURIZED_ELEMENT_SCALE * this.args.profilePictureArgs.width) / 2, (ConstantsExports.TEXTURIZED_ELEMENT_SCALE * this.args.profilePictureArgs.height) / 2, (ConstantsExports.TEXTURIZED_ELEMENT_SCALE * this.args.profilePictureArgs.width) / 2, (ConstantsExports.TEXTURIZED_ELEMENT_SCALE * this.args.profilePictureArgs.height) / 2);
            mask.scale.set(1 / ConstantsExports.TEXTURIZED_ELEMENT_SCALE);
            mask.cursor = 'pointer';
            sprite.mask = mask;
            this.container.addChild(mask);
            this.addGraphic(this.layer, bg, pic.bgColor || this.args.backgroundColor, settings);
            this.addGraphic(this.layer, sprite, undefined, settings);
            return [sprite, bg];
        });
        const headerTextY = this.profilePics.length > 0 ?
            this.profilePics[0][0].y +
                this.profilePics[0][0].height / 2 +
                this.args.expanded.elementPadding +
                this.args.expanded.borderPadding
            : this.state.current.borderPadding;
        const textStyle = new PIXI.TextStyle({
            fontSize: this.args.textScaleFactor * this.args.fontSize,
            fill: ConstantsExports.COLOR_UNFILLED,
            wordWrap: true,
            wordWrapWidth: (this.args.maxWidth - 2 * this.state.current.borderPadding) * this.args.textScaleFactor,
        });
        const authorText = new PIXI.Text(this.args.commenterName, textStyle);
        const resolution = 0.25;
        authorText.resolution = resolution;
        authorText.cacheAsBitmapResolution = resolution;
        authorText.scale.set(1 / this.args.textScaleFactor, 1 / this.args.textScaleFactor);
        authorText.x = this.state.current.borderPadding;
        authorText.y = headerTextY;
        authorText.mask = this.maskRect;
        authorText.cursor = 'pointer';
        this.addGraphic(this.layer, authorText, this.args.foregroundColor, settings);
        const dateText = new PIXI.Text(this.args.timestampStr, textStyle);
        dateText.resolution = resolution;
        dateText.scale.set(1 / this.args.textScaleFactor, 1 / this.args.textScaleFactor);
        dateText.x = authorText.x + authorText.width + this.state.current.elementPadding;
        dateText.y = headerTextY;
        dateText.mask = this.maskRect;
        dateText.cursor = 'pointer';
        this.addGraphic(this.layer, dateText, this.args.subtleColor, settings);
        const commentText = new PIXI.Text(this.maybeElideText(this.args.commentText), textStyle);
        commentText.resolution = resolution;
        commentText.scale.set(1 / this.args.textScaleFactor, 1 / this.args.textScaleFactor);
        commentText.x = this.state.current.borderPadding;
        commentText.y =
            max(authorText.y + authorText.height, dateText.y + dateText.height) + this.state.current.elementPadding;
        commentText.mask = this.maskRect;
        commentText.cursor = 'pointer';
        this.addGraphic(this.layer, commentText, this.args.foregroundColor, settings);
        this.textElements = [authorText, dateText, commentText];
        if (this.args.repliesStr) {
            const repliesText = new PIXI.Text(this.args.repliesStr, textStyle);
            repliesText.scale.set(1 / this.args.textScaleFactor, 1 / this.args.textScaleFactor);
            repliesText.x = this.state.current.borderPadding;
            repliesText.y = commentText.y + commentText.height + this.state.current.elementPadding;
            repliesText.mask = this.maskRect;
            repliesText.cursor = 'pointer';
            this.addGraphic(this.layer, repliesText, this.args.subtleColor, settings);
            this.textElements.push(repliesText);
        }
        this.renderedContentsWidth = maxA(dateText.x + dateText.width - this.state.current.borderPadding, commentText.width, this.profilePics[this.profilePics.length - 1][0].x +
            this.profilePics[this.profilePics.length - 1][0].width -
            this.state.current.borderPadding);
        this.renderedContentsHeight = maxA(...this.textElements.map((t) => t.height + t.y - this.state.current.borderPadding));
        this.state.setStateImmedate(this.getCollapsedState());
        this.drawingManager.addElement(this);
        super.setInteractionEnabled(true);
        super.setHoverable(true);
        this.container.parent.sortableChildren = true;
        if (!this.loadCompleteHandler && unloadedResources) {
            this.loadCompleteHandlerBinding = this.loader.onLoad.add(this.loaderCompleted);
            this.loadErrorHandlerBinding = this.loader.onError.add(this.loaderError);
        }
        this.maybeStartLoad();
    }
    maybeLoadResource(pic) {
        if (!pic.url) {
            return false;
        }
        const queueItem = {
            name: pic.username,
            url: pic.url,
            options: {
                loadType: PIXI.LoaderResource.LOAD_TYPE.IMAGE,
                crossOrigin: true,
            },
        };
        const result = this.maybeLoadQueueItem(queueItem);
        this.loadedResources++;
        return result;
    }
    maybeLoadQueueItem(queueItem) {
        if (this.loader.resources[queueItem.name]) {
            return !this.loader.resources[queueItem.name].isComplete;
        }
        if (this.loader.loading) {
            // Queue
            if (!this.loader._cadstromQueue) {
                this.loader._cadstromQueue = [];
            }
            if (!this.loader._cadstromQueue.find((item) => item.name === queueItem.name)) {
                this.loader._cadstromQueue.push(queueItem);
            }
        }
        else {
            this.loader.add(queueItem.name, queueItem.url, queueItem.options);
        }
        return true;
    }
    maybeStartLoad() {
        if (this.loadedResources > 0) {
            this.loader.load();
        }
    }
    loadTextureForResource(resource) {
        if (this.loader.resources[resource]?.isComplete &&
            !this.loader.resources[resource].error &&
            this.loader.resources[resource].texture) {
            return this.loader.resources[resource].texture;
        }
        const renderChar = ` ${resource.charAt(0)} `;
        if (!this.textureGraphics.has(renderChar)) {
            const graphic = new PIXI.Text(renderChar, this.args.profilePictureArgs.charStyleOpts);
            [graphic.width, graphic.height] = this.getTextureSizeScaled(graphic);
            this.textureGraphics.set(renderChar, graphic);
        }
        return this.textureGraphics.get(renderChar).texture;
    }
    getTextureSizeScaled(texture) {
        let scale;
        if (texture.width > texture.height) {
            scale = this.args.profilePictureArgs.width / texture.width;
        }
        else {
            scale = this.args.profilePictureArgs.height / texture.height;
        }
        return [texture.width * scale, texture.height * scale];
    }
    maybeElideText(commentText) {
        if (commentText.length <= this.args.commentMaxCharLength) {
            return commentText;
        }
        return commentText.substring(0, this.args.commentMaxCharLength) + '…';
    }
    enableTick() {
        if (!this.tickEnabled) {
            this.drawingManager.app.ticker.add(this.tick);
            this.tickEnabled = true;
        }
    }
    disableTick() {
        if (this.tickEnabled) {
            this.drawingManager.app.ticker.remove(this.tick);
            delete this.tickEnabled;
        }
    }
    getExpandedState() {
        let width = max(this.renderedContentsWidth, this.args.profilePictures.length * (this.args.profilePictureArgs.width + this.args.expanded.imageSpacing) -
            +this.args.expanded.imageSpacing);
        if (width > this.args.maxWidth) {
            width = this.args.maxWidth;
        }
        let height = max(this.renderedContentsHeight, this.args.profilePictures.length * this.args.profilePictureArgs.height);
        if (height > this.args.maxHeight) {
            height = this.args.maxHeight;
        }
        return {
            width: width,
            height: height,
            ...this.args.expanded,
            visible: true,
        };
    }
    getCollapsedState() {
        let width = this.args.profilePictures.length * this.args.profilePictureArgs.width +
            (this.args.profilePictures.length - 1) * (this.args.collapsed.imageSpacing + this.args.collapsed.borderPadding);
        if (width > this.args.maxWidth) {
            width = this.args.maxWidth;
        }
        let height = this.args.profilePictureArgs.height;
        if (height > this.args.maxHeight) {
            height = this.args.maxHeight;
        }
        return {
            width,
            height,
            ...this.args.collapsed,
            visible: false,
        };
    }
    tick = () => {
        this.oneShotEnabled = false;
        this.state.tick();
        this.render();
        if (!this.state.isSeeking) {
            this.disableTick();
        }
    };
    zoomed = () => {
        if (!this.tickEnabled && !this.oneShotEnabled) {
            this.drawingManager.app.ticker.addOnce(this.tick);
            this.oneShotEnabled = true;
        }
    };
    hoverStateChanged = () => {
        if (this.isHovered) {
            this.show();
        }
        else if (!this.stayShown) {
            this.hide();
        }
    };
    loaderCompleted = (loader, resource) => {
        if (this.isDestroyed) {
            return;
        }
        if (this.profileResourceIndices.has(resource.name)) {
            const texture = this.loadTextureForResource(resource.name);
            const dimensions = this.getTextureSizeScaled(texture);
            for (const index of this.profileResourceIndices.get(resource.name)) {
                const sprite = this.profilePics[index][0];
                sprite.texture = texture;
                [sprite.width, sprite.height] = dimensions;
            }
        }
        setTimeout(() => {
            if (!loader.loading && loader._cadstromQueue?.length) {
                // enqueue next batch
                let hasWorkToDo = false;
                for (const queueItem of loader._cadstromQueue) {
                    hasWorkToDo = this.maybeLoadQueueItem(queueItem) || hasWorkToDo;
                }
                delete loader._cadstromQueue;
                if (hasWorkToDo) {
                    loader.load();
                }
            }
        }, 0);
    };
    loaderError = (_error, _loader, resource) => {
        if (this.isDestroyed) {
            return;
        }
        if (this.profileResourceIndices.has(resource.name)) {
            const texture = this.loadTextureForResource(resource.name);
            for (const index of this.profileResourceIndices.get(resource.name)) {
                this.profilePics[index][0].texture = texture;
            }
        }
    };
    scaleStyling(styling, factor) {
        return {
            borderPadding: styling.borderPadding * factor,
            borderRadius: styling.borderRadius * factor,
            elementPadding: styling.elementPadding * factor,
            imageSpacing: styling.imageSpacing * factor,
        };
    }
    getWorldLength(value, offsetCoords) {
        if (!offsetCoords) {
            offsetCoords = this.drawingManager.viewport.toWorld(0, 0);
        }
        return this.drawingManager.viewport.toWorld(value, 0).x - offsetCoords.x;
    }
    getCurrentScale() {
        return [this.drawingManager.viewport.scale.x, this.drawingManager.viewport.scale.y];
    }
}

var Color = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': Color$1
});

// As I was writing the probe tool, I just naturally held the probe tool state within the `ProbeTool` class. However,
const DOT_RADIUS = 6;
const OVERLAY_PADDING = 4;
let probeIdCounter = 0; // Used to give each probe a unique ID
const getNewProbeId = () => probeIdCounter++;
const ghostProbeId = -1; // A special ID for identifying the ghost probe
class ProbeTool extends EventEmitter {
    drawingManager;
    static ElementId = 'probe';
    id = ProbeTool.ElementId;
    /** Doesn't seem to do anything */
    enabled = true;
    container;
    get drawingElement() {
        return this.container;
    }
    negProbe = null;
    negProbePos = null;
    negProbeNet = null;
    posProbes = [];
    get ghostProbe() {
        return this.posProbes.find((probe) => probe.id === ghostProbeId);
    }
    get unitsPerPx() {
        return this.drawingManager.viewport.toWorld(1, 0).x - this.drawingManager.viewport.toWorld(0, 0).x;
    }
    /** `ProbeTool` calls this to request the voltage measurement from the frontend. */
    getVoltage;
    constructor(drawingManager, opts) {
        super();
        this.drawingManager = drawingManager;
        if (opts?.getVoltage) {
            this.getVoltage = opts.getVoltage;
        }
        this.container = new PIXI.Container();
    }
    on(eventName, listener) {
        return super.on(eventName, listener);
    }
    emit(eventName, ...args) {
        return super.emit(eventName, ...args);
    }
    enable = () => {
        for (const element of this.drawingManager.elements) {
            if ('net' in element.modelObject && element.modelObject.net !== undefined) {
                element.setHoverable(true);
            }
            else {
                element.setHoverable(false);
            }
            element.setInteractionEnabled(true);
        }
        this.drawingManager.on('elementPointerDown', this.onDrawingClick);
        this.drawingManager.on('elementHoverStateChanged', this.onElementHoverStateChanged);
        this.drawingManager.app.ticker.add(this.render);
        this.container.visible = true;
        this.highlightNetsWithProbes();
    };
    disable = () => {
        this.container.visible = false;
        this.drawingManager.removeListener('elementPointerDown', this.onDrawingClick);
        this.drawingManager.removeListener('elementHoverStateChanged', this.onElementHoverStateChanged);
        this.drawingManager.app.ticker.remove(this.render);
        for (const element of this.drawingManager.elements) {
            element.setHoverable(true);
            element.setInteractionEnabled(true);
            element.setSelected(false, false);
        }
    };
    destroy = () => {
        this.drawingManager.elements.forEach((element) => element.setHoverable(true));
        this.drawingManager.setSelectionMode(SelectionMode.None);
        this.drawingManager.app.ticker.remove(this.render);
        this.drawingManager.removeListener('elementPointerDown', this.onDrawingClick);
        this.drawingManager.removeListener('elementHoverStateChanged', this.onElementHoverStateChanged);
        this.container.destroy({ children: true });
    };
    onElementHoverStateChanged = (element) => {
        if (element.modelObject.net !== undefined && element.isHoveredIrregardlessOfSelection && element.isVisible) {
            // Create the ghost probe if it doesn't exist already
            if (this.ghostProbe) {
                return;
            }
            const dot = this.makeDotGraphic('pos');
            this.container.addChild(dot);
            const { overlay, overlayBg, overlayText } = this.makeOverlay();
            this.container.addChild(overlay);
            this.posProbes.push({
                id: ghostProbeId,
                net: element.modelObject.net,
                pos: [0, 0],
                dot,
                overlay,
                overlayBg,
                overlayText,
            });
        }
        else {
            // Remove the probe ghost
            this.ghostProbe?.dot.destroy();
            this.ghostProbe?.overlay.destroy();
            this.ghostProbe?.overlayBg.destroy();
            this.ghostProbe?.overlayText.destroy();
            this.posProbes = this.posProbes.filter((probe) => probe.id !== ghostProbeId);
        }
    };
    onPointerMove = (parsedEvent) => {
        if (!this.ghostProbe) {
            return;
        }
        this.ghostProbe.pos = [parsedEvent.world.x, parsedEvent.world.y];
    };
    onDrawingClick = (event, element) => {
        // Ignore clicks on elements not directly associated with a net
        if (!element.modelObject.net) {
            return;
        }
        const mouseButton = getMouseButtonForEvent(event);
        const clickPosWorld = this.drawingManager.viewport.toWorld(event.data.global);
        if (mouseButton === MouseEventButtons.Left) {
            const newProbeId = this.placeNewPosProbe([clickPosWorld.x, clickPosWorld.y], element.modelObject.net);
            this.emit('placePosProbe', newProbeId, element.modelObject.net);
            this.highlightNetsWithProbes();
        }
        else if (mouseButton === MouseEventButtons.Right) {
            this.negProbePos = [clickPosWorld.x, clickPosWorld.y];
            this.negProbeNet = element.modelObject.net;
            this.emit('placeNegProbe', this.negProbeNet);
        }
    };
    /** Iterate through drawing elements and set them to selected if they belong to any nets with probes on them */
    highlightNetsWithProbes = () => {
        const deselectAll = this.posProbes.length === 0;
        for (const element of this.drawingManager.elements) {
            if (deselectAll) {
                element.setSelected(false, false);
            }
            else if (this.posProbes.some((probe) => 'net' in element.modelObject && probe.net === element.modelObject.net)) {
                element.setSelected(true, true);
            }
            else {
                element.setSelected(false, true);
            }
            element.setInteractionEnabled(true);
        }
    };
    placeNewPosProbe(pos, net) {
        const id = getNewProbeId();
        const dot = this.makeDotGraphic('pos', { probeId: id, interactive: true });
        this.container.addChild(dot);
        const { overlay, overlayBg, overlayText } = this.makeOverlay();
        this.container.addChild(overlay);
        this.posProbes.push({ id, dot, net, pos, overlay, overlayBg, overlayText });
        return id;
    }
    removePosProbe = (id) => {
        const probe = this.posProbes.find((probe) => probe.id === id);
        if (!probe) {
            return;
        }
        this.posProbes = this.posProbes.filter((probe) => probe.id !== id);
        probe.dot.destroy();
        probe.overlay.destroy({ children: true });
        this.highlightNetsWithProbes();
        this.emit('removePosProbe', id);
    };
    makeDotGraphic(type, opts) {
        const dot = new PIXI.Graphics().beginFill(ConstantsExports.COLOR_UNFILLED).drawCircle(0, 0, DOT_RADIUS).endFill();
        const renderOpts = this.drawingManager.colorProvider.getColorForTool('probe', type);
        dot.tint = renderOpts.selected.tint;
        dot.interactive = opts?.interactive ?? false;
        // Remove the probe when the dot is clicked. You can't manually remove the ghost probe.
        const probeId = opts?.probeId;
        if (probeId !== undefined && probeId !== ghostProbeId) {
            // Reason for the `setTimeout`:
            // The probe is placed via a click (which for historical reasons happens on pointer down, not up). When that click
            // ends, a pointer up event would then be fired on the freshly placed probe, removing it. So we wait a second and
            // the user will probably have ended the click once we place the event listener.
            setTimeout(() => {
                dot.on('pointerup', () => {
                    if (type === 'neg') {
                        this.negProbePos = null;
                        this.negProbeNet = 'GND';
                        this.emit('placeNegProbe', 'GND');
                        dot.destroy();
                    }
                    else {
                        this.removePosProbe(probeId);
                    }
                });
            }, 1000);
        }
        return dot;
    }
    makeOverlay() {
        const overlay = new PIXI.Container();
        const overlayBg = new PIXI.Graphics();
        const overlayText = new PIXI.Text('', { fontFamily: 'monospace', fontSize: 16, fill: 0x000000 });
        overlayText.position.set(OVERLAY_PADDING, OVERLAY_PADDING);
        overlay.addChild(overlayBg);
        overlay.addChild(overlayText);
        return { overlay, overlayBg, overlayText };
    }
    prevNegProbeNet = null;
    prevNegProbePos = null;
    prevPosProbes = [];
    prevUnitsPerPx = undefined;
    render = () => {
        const hasZoomChanged = this.prevUnitsPerPx !== this.unitsPerPx;
        // If the negative probe has moved, update its position
        const hasNegProbePosChanged = this.negProbePos !== this.prevNegProbePos;
        if (this.negProbePos !== null && (hasNegProbePosChanged || hasZoomChanged)) {
            // Create the negative probe if it doesn't exist already
            if (!this.negProbe) {
                this.negProbe = this.makeDotGraphic('neg');
                this.negProbe.interactive = false;
                this.container.addChild(this.negProbe);
            }
            // Set the position
            this.negProbe.position.set(this.negProbePos[0], this.negProbePos[1]);
            this.negProbe.scale.set(this.unitsPerPx, this.unitsPerPx);
        }
        /** Draws the positive probes and their overlays */
        const updateProbeOverlay = (posProbe) => {
            posProbe.dot.position.set(posProbe.pos[0], posProbe.pos[1]);
            posProbe.dot.scale.set(this.unitsPerPx, this.unitsPerPx);
            posProbe.overlay.position.set(posProbe.pos[0] + DOT_RADIUS * 1.5 * this.unitsPerPx, posProbe.pos[1] + DOT_RADIUS * 1.5 * this.unitsPerPx);
            posProbe.overlay.scale.set(this.unitsPerPx, this.unitsPerPx);
            if (this.negProbeNet) {
                const voltageDifference = this.getVoltage?.(this.negProbeNet, posProbe.net);
                posProbe.overlayText.text = `${posProbe.net}: ${voltageDifference ? `${voltageDifference} V` : 'undefined'}`;
                posProbe.overlayBg
                    .clear()
                    .beginFill(0xffffff, 0.8)
                    .lineStyle(1, 0xffffff, 1)
                    .drawRoundedRect(0, 0, posProbe.overlayText.width + 2 * OVERLAY_PADDING, posProbe.overlayText.height + 2 * OVERLAY_PADDING, 4)
                    .endFill();
            }
        };
        const hasNegProbeNetChanged = this.negProbeNet !== this.prevNegProbeNet;
        const changedPosProbes = this.posProbes.filter((posProbe) => {
            const prevPosProbe = this.prevPosProbes.find((prevPosProbe) => prevPosProbe.id === posProbe.id);
            return posProbe.pos !== prevPosProbe?.pos || posProbe.net !== prevPosProbe?.net;
        });
        if (hasZoomChanged || hasNegProbeNetChanged || changedPosProbes.length > 0) {
            this.posProbes.forEach(updateProbeOverlay);
        }
        this.prevNegProbePos = this.negProbePos;
        this.prevPosProbes = this.posProbes.map((posProbe) => ({ ...posProbe })); // One-level-deep copy
        this.prevUnitsPerPx = this.unitsPerPx;
    };
}

var TransformExports = requireTransform();

var UtilExports = requireUtil();

var MeasurementMode;
(function (MeasurementMode) {
    MeasurementMode[MeasurementMode["Horizontal"] = 0] = "Horizontal";
    MeasurementMode[MeasurementMode["Vertical"] = 1] = "Vertical";
    MeasurementMode[MeasurementMode["Direct"] = 2] = "Direct";
})(MeasurementMode || (MeasurementMode = {}));
class InteractiveRulerTool extends EventEmitter {
    drawingManager;
    colorProvider;
    markIntervals;
    static ElementId = 'iruler';
    static OptTextShadowColor = 'textShadow';
    static LineShadowDeltaWidth = 1;
    container;
    start;
    end;
    _lineWidth = 2;
    _fontSize = 16;
    oneShotEnabled;
    listenersSubscribed = false;
    singleAxisOnly = false;
    take = 1;
    hasRealEnd = false;
    renderOpts;
    shadowRenderOpts;
    id = InteractiveRulerTool.ElementId;
    enabled = true;
    get drawingElement() {
        return this.container;
    }
    constructor(drawingManager, colorProvider, markIntervals) {
        super();
        this.drawingManager = drawingManager;
        this.colorProvider = colorProvider;
        this.markIntervals = markIntervals;
        this.container = new PIXI.Container();
        this.container.visible = false;
        this.renderOpts = colorProvider.getColorForTool(InteractiveRulerTool.ElementId);
        this.shadowRenderOpts = colorProvider.getColorForTool(InteractiveRulerTool.ElementId, InteractiveRulerTool.OptTextShadowColor);
    }
    get fontSize() {
        return this._fontSize;
    }
    set fontSize(value) {
        if (value !== this.fontSize) {
            this._fontSize = value;
            this.render();
        }
    }
    get lineWidth() {
        return this._lineWidth;
    }
    set lineWidth(value) {
        if (this._lineWidth !== value) {
            this._lineWidth = value;
            this.render();
        }
    }
    get hasSelection() {
        return Boolean(this.start) && Boolean(this.end);
    }
    assertHasSelection() {
        if (!this.hasSelection) {
            throw new Error('Missing selection');
        }
    }
    screenCoords() {
        this.assertHasSelection();
        switch (this.getMeasurementMode()) {
            case MeasurementMode.Horizontal:
                return {
                    x1: this.start.screen.x,
                    y1: this.start.screen.y,
                    x2: this.end.screen.x,
                    y2: this.start.screen.y,
                };
            case MeasurementMode.Vertical:
                return {
                    x1: this.start.screen.x,
                    y1: this.start.screen.y,
                    x2: this.start.screen.x,
                    y2: this.end.screen.y,
                };
        }
        return {
            x1: this.start.screen.x,
            y1: this.start.screen.y,
            x2: this.end.screen.x,
            y2: this.end.screen.y,
        };
    }
    drawingCoords() {
        this.assertHasSelection();
        switch (this.getMeasurementMode()) {
            case MeasurementMode.Horizontal:
                return {
                    x1: this.start.world.x,
                    y1: this.start.world.y,
                    x2: this.end.world.x,
                    y2: this.start.world.y,
                };
            case MeasurementMode.Vertical:
                return {
                    x1: this.start.world.x,
                    y1: this.start.world.y,
                    x2: this.start.world.x,
                    y2: this.end.world.y,
                };
        }
        return {
            x1: this.start.world.x / ConstantsExports.WORLD_SCALE,
            y1: -this.start.world.y / ConstantsExports.WORLD_SCALE,
            x2: this.end.world.x / ConstantsExports.WORLD_SCALE,
            y2: -this.end.world.y / ConstantsExports.WORLD_SCALE,
        };
    }
    rendererCoords() {
        this.assertHasSelection();
        switch (this.getMeasurementMode()) {
            case MeasurementMode.Horizontal:
                return {
                    x1: this.start.global.x,
                    y1: this.start.global.y,
                    x2: this.end.global.x,
                    y2: this.start.global.y,
                };
            case MeasurementMode.Vertical:
                return {
                    x1: this.start.global.x,
                    y1: this.start.global.y,
                    x2: this.start.global.x,
                    y2: this.end.global.y,
                };
        }
        return {
            x1: this.start.global.x,
            y1: this.start.global.y,
            x2: this.end.global.x,
            y2: this.end.global.y,
        };
    }
    setStart(event) {
        this.start = event;
        this.render();
        this.updateEventSubscriptions();
    }
    setEnd(event) {
        this.end = event;
        this.render();
        this.emit('rulerUpdated', this);
    }
    onPointerDown(parsedEvent, event) {
        if (getNumTouchesForEvent(event) > 1 ||
            this.drawingManager.isDragging ||
            parsedEvent.button !== MouseEventButtons.Left) {
            return;
        }
        this.singleAxisOnly = Boolean(parsedEvent.shiftKey);
        if ((this.end && this.hasRealEnd) || !this.start) {
            this.hasRealEnd = false;
            this.clear();
            this.setStart(parsedEvent);
            this.drawingManager.disableViewportDragDuringCurrentDrag();
        }
        else if (this.start) {
            this.hasRealEnd = true;
            this.setEnd(parsedEvent);
        }
    }
    onDragStart(parsedEvent, event) {
        if (getNumTouchesForEvent(event) > 1) {
            return;
        }
        this.clear();
        this.singleAxisOnly = Boolean(parsedEvent.shiftKey);
        this.setStart(parsedEvent);
        this.drawingManager.disableViewportDragDuringCurrentDrag();
        this.hasRealEnd = false;
    }
    onDragEnd(parsedEvent, event) {
        if (getNumTouchesForEvent(event) > 1 || !this.start) {
            return;
        }
        this.setEnd(parsedEvent);
        this.hasRealEnd = true;
    }
    onPointerMove(parsedEvent, event) {
        if ((!this.drawingManager.isDragging && (this.hasRealEnd || !this.start)) || getNumTouchesForEvent(event) > 1) {
            return;
        }
        this.singleAxisOnly = Boolean(parsedEvent.shiftKey);
        if (!this.start) {
            this.drawingManager.disableViewportDragDuringCurrentDrag();
            this.setStart(parsedEvent);
        }
        else {
            this.setEnd(parsedEvent);
        }
    }
    clear() {
        if (this.start) {
            delete this.start;
        }
        if (this.end) {
            delete this.end;
        }
        this.updateEventSubscriptions();
        this.removeChildren();
        this.container.visible = false;
        this.emit('rulerCleared', this);
    }
    removeChildren() {
        for (const child of this.container.removeChildren()) {
            child.destroy();
        }
    }
    getMeasurementMode() {
        if (!this.start || !this.end) {
            return this.singleAxisOnly ? MeasurementMode.Horizontal : MeasurementMode.Direct;
        }
        if (this.singleAxisOnly) {
            if (Math.abs(this.end.world.x - this.start.world.x) > Math.abs(this.end.world.y - this.start.world.y)) {
                return MeasurementMode.Horizontal;
            }
            else {
                return MeasurementMode.Vertical;
            }
        }
        return MeasurementMode.Direct;
    }
    render() {
        if (!this.start || !this.end) {
            if (this.start) {
                this.renderStartingCrosshair();
            }
            return;
        }
        this.container.visible = true;
        this.removeChildren();
        this.container.x = this.start.world.x;
        this.container.y = this.start.world.y;
        const visibleBounds = this.drawingManager.viewport.getVisibleBounds();
        const startInBounds = visibleBounds.contains(this.start.world.x, this.start.world.y);
        const endInBounds = visibleBounds.contains(this.end.world.x, this.end.world.y);
        const fullyContained = startInBounds && endInBounds;
        let intersections = [];
        const start = [this.start.world.x, this.start.world.y];
        const end = [this.end.world.x, this.end.world.y];
        if (!fullyContained) {
            // Top
            intersections[0] = this.checkLinesIntersect(start, end, [visibleBounds.x, visibleBounds.y], [visibleBounds.x + visibleBounds.width, visibleBounds.y]);
            // Right
            intersections[1] = this.checkLinesIntersect(start, end, [visibleBounds.x + visibleBounds.width, visibleBounds.y], [visibleBounds.x + visibleBounds.width, visibleBounds.y + visibleBounds.height]);
            // Bottom
            intersections[2] = this.checkLinesIntersect(start, end, [visibleBounds.x, visibleBounds.y + visibleBounds.height], [visibleBounds.x + visibleBounds.width, visibleBounds.y + visibleBounds.height]);
            // Left
            intersections[3] = this.checkLinesIntersect(start, end, [visibleBounds.x, visibleBounds.y], [visibleBounds.x, visibleBounds.y + visibleBounds.height]);
            intersections = intersections
                .filter((i) => !!i)
                .sort((a, b) => distanceBetweenPoints(start, a) - distanceBetweenPoints(start, b));
        }
        if (!fullyContained && !intersections.find((i) => Boolean(i))) {
            // Whole ruler is off-screen
            return;
        }
        let width = this.end.world.x - this.start.world.x;
        let height = this.end.world.y - this.start.world.y;
        if (this.singleAxisOnly) {
            if (Math.abs(width) > Math.abs(height)) {
                height = 0;
            }
            else {
                width = 0;
            }
        }
        const length = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
        const widthModifier = this.drawingManager.viewport.toWorld(1, 0).x - this.drawingManager.viewport.toWorld(0, 0).x;
        if (length === 0) {
            return;
        }
        // Calculate the line length
        let lineAngleRad = height > 0 ? -Math.PI / 2 : Math.PI / 2;
        if (width !== 0) {
            lineAngleRad = Math.atan(height / width);
        }
        if (width > 0) {
            lineAngleRad += Math.PI;
        }
        const lineAngle = UtilsExports.radiansToDegrees(lineAngleRad);
        // Setup the graphics objects
        const g = new PIXI.Graphics();
        const g2 = new PIXI.Graphics();
        this.container.addChild(g2, g);
        g2.tint = 0x00;
        g.tint = this.renderOpts.selected.tint;
        g.lineStyle(widthModifier * this.lineWidth, ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
        g2.lineStyle(widthModifier * (this.lineWidth + InteractiveRulerTool.LineShadowDeltaWidth), ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
        //// Draw the main ruler line
        g.moveTo(0, 0);
        g.lineTo(width, height);
        g2.moveTo(0, 0);
        g2.lineTo(width, height);
        //// Draw crosshairs at each end
        const tickLength = maxA(10, ...this.markIntervals.map((i) => i.markLength)) * widthModifier;
        for (let i = 0; i < 4; ++i) {
            g.moveTo(0, 0);
            g2.moveTo(0, 0);
            let end = TransformExports.rotatePoint([0, tickLength], lineAngle + 90 * i);
            if (i !== 1) {
                g.lineTo(end[0], end[1]);
                g2.lineTo(end[0], end[1]);
            }
            if (i !== 3) {
                end = TransformExports.translatePoint(end, [width, height]);
                g.moveTo(width, height);
                g.lineTo(end[0], end[1]);
                g2.moveTo(width, height);
                g2.lineTo(end[0], end[1]);
            }
        }
        //// Draw the midpoint line
        const textStyle = new PIXI.TextStyle({
            fontSize: this.fontSize,
            fontWeight: 'bold',
            fontFamily: 'monospace',
            align: 'center',
            fill: this.renderOpts.selected.tint,
            dropShadow: true,
            dropShadowAlpha: this.shadowRenderOpts.selected.alpha,
            dropShadowColor: this.shadowRenderOpts.selected.tint,
            dropShadowDistance: 0,
            dropShadowBlur: this.fontSize / 7.5,
        });
        {
            const midPoint = [width / 2, height / 2];
            g.moveTo(midPoint[0], midPoint[1]);
            g2.moveTo(midPoint[0], midPoint[1]);
            const midPointEnd = TransformExports.translatePoint(midPoint, TransformExports.rotatePoint([0, tickLength / 2], lineAngle + 180));
            g.lineTo(midPointEnd[0], midPointEnd[1]);
            g2.lineTo(midPointEnd[0], midPointEnd[1]);
            const valueToDisplay = UtilExports.formatValueForGrid(length / ConstantsExports.WORLD_SCALE, this.drawingManager.currentGrid, false);
            const lengthText = new PIXI.Text(`${valueToDisplay} ${this.drawingManager.currentGrid.units}`, textStyle);
            // lengthText.resolution = min(TEXTURIZED_ELEMENT_SCALE / 10 / widthModifier, 1000);
            lengthText.anchor.set(
            // Below, dividing by 1.8 instead of 2 (to stay within the bounds ~[0, 1]),
            // effectively creates some padding around the edges of the text
            Math.sin(lineAngleRad + Math.PI) / 1.8 + 0.5, Math.cos(lineAngleRad) / 1.8 + 0.5);
            lengthText.scale.set(widthModifier, widthModifier);
            lengthText.x = midPointEnd[0];
            lengthText.y = midPointEnd[1];
            lengthText.alpha = this.renderOpts.selected.alpha;
            this.container.addChild(lengthText);
        }
        //// Draw the marks on the ruler
        const markLengths = new Map();
        const gridIntervals = this.drawingManager.currentGrid?.unitSize || 1;
        const drawingLength = length / ConstantsExports.WORLD_SCALE;
        // Scale the number of marks to the zoom level of the screen
        const intervalLength = this.getScreenLength(gridIntervals);
        while (intervalLength * this.take < this.lineWidth * 10) {
            this.take = this.take * 5;
        }
        while (intervalLength * this.take > this.lineWidth * 20) {
            this.take = this.take / 5;
        }
        // Calculate the position on the line that first appears within visible bounds
        let minLineLength = 0;
        let maxLineLength = drawingLength;
        if (intersections.length === 2) {
            // Two intersections means that the start and end are out of bounds. In this case, the intersections are sorted
            // and the first is the closest to the start
            minLineLength = max(minLineLength, distanceBetweenPoints(start, intersections[0]) / ConstantsExports.WORLD_SCALE);
            maxLineLength = min(maxLineLength, distanceBetweenPoints(start, intersections[1]) / ConstantsExports.WORLD_SCALE);
        }
        else {
            for (const intersection of intersections) {
                if (intersection) {
                    // Intersection with top line
                    if (startInBounds) {
                        maxLineLength = min(maxLineLength, distanceBetweenPoints(start, intersection) / ConstantsExports.WORLD_SCALE);
                    }
                    else {
                        minLineLength = max(minLineLength, distanceBetweenPoints(start, intersection) / ConstantsExports.WORLD_SCALE);
                    }
                }
            }
        }
        // Calculate the length of each tick mark
        for (const interval of this.markIntervals) {
            for (let markPoint = TransformExports.floorToMultiple(minLineLength, interval.interval * gridIntervals * this.take); markPoint < maxLineLength; markPoint += interval.interval * gridIntervals * this.take) {
                const usePoint = TransformExports.roundToMultiple(markPoint, 0.0001);
                const current = markLengths.get(usePoint);
                if (!current || current.markLength < interval.markLength) {
                    markLengths.set(usePoint, interval);
                }
            }
        }
        // Draw the tick marks
        for (let [pos, interval] of markLengths.entries()) {
            pos = pos * ConstantsExports.WORLD_SCALE;
            const markWidth = widthModifier * (interval.markWidth || this.lineWidth);
            g.lineStyle(markWidth, ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
            g2.lineStyle(widthModifier * (InteractiveRulerTool.LineShadowDeltaWidth + (interval.markWidth || this.lineWidth)), ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
            const start = [(width * pos) / length, (height * pos) / length];
            const end = TransformExports.translatePoint(start, TransformExports.rotatePoint([0, widthModifier * interval.markLength], lineAngle));
            g.moveTo(start[0], start[1]);
            g.lineTo(end[0], end[1]);
            g2.moveTo(start[0], start[1]);
            g2.lineTo(end[0], end[1]);
            if (!interval.renderLabel || pos === 0) {
                continue;
            }
            const valueToDisplay = UtilExports.formatValueForGrid(pos / ConstantsExports.WORLD_SCALE, this.drawingManager.currentGrid, false);
            const labelText = new PIXI.Text(`${valueToDisplay} ${this.drawingManager.currentGrid.units}`, textStyle);
            labelText.anchor.set(1.1, 0.5);
            labelText.angle = lineAngle - 90;
            labelText.x = end[0];
            labelText.y = end[1];
            labelText.alpha = this.renderOpts.selected.alpha;
            labelText.scale.set(widthModifier, widthModifier);
            this.container.addChild(labelText);
        }
    }
    renderStartingCrosshair() {
        if (!this.start) {
            return;
        }
        // This is a temporary hack until we have a nicer solution
        this.container.visible = true;
        this.removeChildren();
        this.container.x = this.start.world.x;
        this.container.y = this.start.world.y;
        const g = new PIXI.Graphics();
        this.container.addChild(g);
        g.tint = this.renderOpts.selected.tint;
        const widthModifier = this.drawingManager.viewport.toWorld(1, 0).x - this.drawingManager.viewport.toWorld(0, 0).x;
        g.lineStyle(widthModifier * this.lineWidth, ConstantsExports.COLOR_UNFILLED, this.renderOpts.selected.alpha);
        //// Draw crosshairs at each end
        const tickLength = maxA(10, ...this.markIntervals.map((i) => i.markLength)) * widthModifier;
        for (let i = 0; i < 4; ++i) {
            g.moveTo(0, 0);
            const end = TransformExports.rotatePoint([0, tickLength], 90 * i);
            g.lineTo(end[0], end[1]);
        }
    }
    /**
     * Checks if two lines intersect, and returns the intersection if they do
     */
    checkLinesIntersect(start1, end1, start2, end2) {
        // calculate the direction of the lines
        const uA = ((end2[0] - start2[0]) * (start1[1] - start2[1]) - (end2[1] - start2[1]) * (start1[0] - start2[0])) /
            ((end2[1] - start2[1]) * (end1[0] - start1[0]) - (end2[0] - start2[0]) * (end1[1] - start1[1]));
        const uB = ((end1[0] - start1[0]) * (start1[1] - start2[1]) - (end1[1] - start1[1]) * (start1[0] - start2[0])) /
            ((end2[1] - start2[1]) * (end1[0] - start1[0]) - (end2[0] - start2[0]) * (end1[1] - start1[1]));
        if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
            return [start1[0] + uA * (end1[0] - start1[0]), start1[1] + uA * (end1[1] - start1[1])];
        }
        return undefined;
    }
    getScreenLength(value, offsetCoords) {
        if (!offsetCoords) {
            offsetCoords = this.drawingManager.viewport.toScreen(0, 0);
        }
        return this.drawingManager.viewport.toScreen(value * ConstantsExports.WORLD_SCALE, 0).x - offsetCoords.x;
    }
    destroy() {
        if (this.start) {
            this.clear();
        }
        this.updateEventSubscriptions();
        if (!this.container.destroyed) {
            this.container.destroy();
        }
    }
    updateEventSubscriptions() {
        if (this.start) {
            window.document.addEventListener('keydown', this.onKeyPress);
            window.document.addEventListener('keyup', this.onKeyPress);
            this.drawingManager.viewport.on('zoomed', this.zoomedOrMoved);
            this.drawingManager.viewport.on('moved', this.zoomedOrMoved);
            this.listenersSubscribed = true;
        }
        else if (this.listenersSubscribed) {
            window.document.removeEventListener('keydown', this.onKeyPress);
            window.document.removeEventListener('keyup', this.onKeyPress);
            this.drawingManager.viewport.removeListener('zoomed', this.zoomedOrMoved);
            this.drawingManager.viewport.removeListener('moved', this.zoomedOrMoved);
            this.listenersSubscribed = false;
        }
    }
    tick = () => {
        this.oneShotEnabled = false;
        this.render();
    };
    zoomedOrMoved = () => {
        if (!this.oneShotEnabled) {
            this.drawingManager.app.ticker.addOnce(this.tick);
            this.oneShotEnabled = true;
        }
    };
    onKeyPress = (e) => {
        if (this.drawingManager.isDragging) {
            this.singleAxisOnly = Boolean(e.shiftKey);
            this.render();
            if (this.start && this.end) {
                this.emit('rulerUpdated', this);
            }
        }
    };
    on(eventName, listener) {
        return super.on(eventName, listener);
    }
}

function enableDblClick(mgr) {
    const superAddEvents = mgr.addEvents;
    const superRemoveEvents = mgr.removeEvents;
    /**
     * To add DOM native 'dblclick' event to the manager
     * @private
     * @extends
     */
    mgr.addEvents = function () {
        superAddEvents.call(this);
        this.onDoubleClick = this.onDoubleClick.bind(this);
        this.processDoubleClick = this.processDoubleClick.bind(this);
        if (this.interactionDOMElement) {
            this.interactionDOMElement.addEventListener('dblclick', this.onDoubleClick, true);
        }
    };
    /**
     * @private
     * @extends
     */
    mgr.removeEvents = function () {
        if (this.interactionDOMElement) {
            this.interactionDOMElement.removeEventListener('dblclick', this.onDoubleClick, true);
        }
        superRemoveEvents.call(this);
    };
    /**
     * Processes the result of the double click check and dispatches the event
     *
     * @private
     * @param {PIXI.interaction.InteractionEvent} interactionEvent - The interaction event wrapping the DOM event
     * @param {PIXI.Container|PIXI.Sprite|PIXI.TilingSprite} displayObject - The display object that was tested
     * @param {boolean} hit - the result of the hit test on the display object
     */
    mgr.processDoubleClick = function (interactionEvent, displayObject, hit) {
        var data = interactionEvent.data;
        var id = interactionEvent.data.identifier;
        if (hit) {
            if (!displayObject.trackedPointers[id]) {
                displayObject.trackedPointers[id] = new InteractionTrackingData(id);
            }
            if (data.pointerType === 'mouse') {
                this.dispatchEvent(displayObject, 'dblclick', interactionEvent);
            }
        }
    };
    /**
     * @param {PointerEvent} originalEvent
     */
    mgr.onDoubleClick = function (originalEvent) {
        // if we support touch events, then only use those for touch events, not pointer events
        if (this.supportsTouchEvents && originalEvent.pointerType === 'touch') {
            return;
        }
        const events = this.normalizeToPointerData(originalEvent);
        if (this.autoPreventDefault && events[0].isNormalized) {
            const cancelable = originalEvent.cancelable || !('cancelable' in originalEvent);
            if (cancelable) {
                originalEvent.preventDefault();
            }
        }
        const eventLen = events.length;
        for (let i = 0; i < eventLen; i++) {
            const event = events[i];
            const interactionData = this.getInteractionDataForPointerId(event);
            const interactionEvent = this.configureInteractionEventForDOMEvent(this.eventData, event, interactionData);
            interactionEvent.data.originalEvent = originalEvent;
            this.processInteractive(interactionEvent, this.renderer._lastObjectRendered, this.processDoubleClick, true);
            this.emit('dblclick', interactionEvent);
        }
    };
}
enableDblClick(InteractionManager.prototype);

class ModelRenderer extends EventEmitter {
    canvas;
    modelBbox;
    initialZoombox;
    opts;
    app;
    viewport;
    resizer;
    resolutionQuery;
    dragOpts;
    pinchOpts;
    wheelOpts;
    bounceOpts;
    constructor(canvas, modelBbox, initialZoombox, opts) {
        super();
        this.canvas = canvas;
        this.modelBbox = modelBbox;
        this.initialZoombox = initialZoombox;
        this.opts = opts;
        this.dragOpts = opts.dragOpts || {};
        this.pinchOpts = opts.pinchOpts || {};
        this.wheelOpts = opts.wheelOpts || {};
        this.bounceOpts = opts.bounceOpts || {};
    }
    init() {
        this.app = new PIXI.Application({
            width: this.canvas.getBoundingClientRect().width,
            height: this.canvas.getBoundingClientRect().height,
            backgroundAlpha: 0,
            view: this.canvas,
            antialias: true,
            resolution: window.devicePixelRatio,
        });
        const interactionManager = this.getInteractionManager();
        if (interactionManager) {
            enableDblClick(interactionManager);
            // @ts-expect-error private method
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            interactionManager.addEvents();
        }
        this.app.stage = new Stage();
        this.viewport = this.createViewport();
        this.resizer = new ResizeObserver(this.handleResized);
        this.resizer.observe(this.canvas);
        this.watchResolution();
    }
    getApp() {
        return this.app;
    }
    getViewport() {
        return this.viewport;
    }
    setDragOpts(opts) {
        this.dragOpts = {
            ...this.dragOpts,
            ...opts,
        };
        this.viewport?.drag(this.dragOpts);
    }
    get isAlive() {
        return Boolean(this.app);
    }
    pauseTicker() {
        if (this.app && this.app.ticker) {
            this.app.ticker.stop();
        }
    }
    resumeTicker() {
        if (this.app && this.app.ticker) {
            this.app.ticker.start();
        }
    }
    isTickerRunning() {
        return this.app && this.app.ticker ? this.app.ticker.started : false;
    }
    destroy() {
        this.resizer?.disconnect();
        this.resolutionQuery?.removeEventListener('change', this.handleResolutionChanged);
        this.resolutionQuery = undefined;
        this.viewport?.destroy();
        this.app?.destroy(false, { children: true });
        // Remove all event listeners
        this.removeAllListeners();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.resizer = undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.viewport = undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.app = undefined;
        if (this.canvas) {
            this.canvas.height = 1;
            this.canvas.width = 1;
            this.canvas.getContext('2d')?.clearRect(0, 0, 1, 1);
            {
                const gl = this.canvas.getContext('webgl');
                if (gl) {
                    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
                }
            }
            {
                const gl = this.canvas.getContext('webgl2');
                if (gl) {
                    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
                }
            }
        }
    }
    fitWorld() {
        this.viewport.fitWorld();
        this.app.render();
    }
    centerDrawing() {
        const bbox = this.getBbox();
        const worldWidth = bbox.maxx - bbox.minx;
        const worldHeight = bbox.maxy - bbox.miny;
        this.centerDrawingInternal(this.viewport, bbox.minx, bbox.miny, worldHeight, worldWidth);
    }
    centerDrawingInternal(viewport, minx, miny, worldHeight, worldWidth) {
        viewport.moveCenter(minx + worldWidth / 2, miny + worldHeight / 2);
    }
    /**
     * Zoom and centre on the specified rectangle, with padding expressed as a fraction of the final viewport size.
     * @param rect             The rectangle to zoom on.
     * @param paddingFraction  Fraction of the final viewport width/height to use as padding on each side.
     *                         e.g. 0.3 means 30% padding on each side, so the item fills the centre 40%.
     */
    zoomAndCenterRect(rect, paddingFraction) {
        const itemWidth = Math.abs(rect.x2 - rect.x1);
        const itemHeight = Math.abs(rect.y2 - rect.y1);
        // Derive the per-side buffer so that padding / (item + 2*buffer) === paddingFraction.
        // Formula is only valid for paddingFraction in [0, 0.5): clamp to avoid divide-by-zero or negative buffers.
        const clampedFraction = Math.max(0, Math.min(paddingFraction, 0.49));
        const bufferRatio = clampedFraction / (1 - 2 * clampedFraction);
        const expandedRect = expandRect(rect, itemWidth * bufferRatio, itemHeight * bufferRatio, false);
        const width = Math.abs(expandedRect.x2 - expandedRect.x1);
        const height = Math.abs(expandedRect.y2 - expandedRect.y1);
        this.viewport.fit(true, width, height);
        this.viewport.moveCenter(expandedRect.x1 + width / 2, expandedRect.y1 + height / 2);
    }
    takeScreenshot(screenshotRect) {
        let frame;
        if (screenshotRect) {
            const stage = {
                x1: this.app.stage.x,
                y1: this.app.stage.y,
                x2: this.app.stage.x + this.app.stage.width,
                y2: this.app.stage.y + this.app.stage.height,
            };
            screenshotRect = constrainRect(screenshotRect, stage);
            frame = new PIXI.Rectangle(screenshotRect.x1, screenshotRect.y1, screenshotRect.x2 - screenshotRect.x1, screenshotRect.y2 - screenshotRect.y1);
        }
        return new Promise((resolve, reject) => {
            try {
                const extract = this.app.renderer.plugins.extract;
                extract.canvas(this.app.stage, frame).toBlob((blob) => {
                    resolve(blob);
                }, 'image/png');
            }
            catch (e) {
                reject(e);
            }
        });
    }
    createViewport() {
        const bbox = this.getBbox();
        const worldWidth = bbox.maxx - bbox.minx;
        const worldHeight = bbox.maxy - bbox.miny;
        const viewport = new Viewport({
            screenWidth: this.canvas.clientWidth,
            screenHeight: this.canvas.clientHeight,
            worldWidth,
            worldHeight,
            passiveWheel: false,
            interaction: this.getInteractionManager(),
        });
        viewport.fit();
        viewport.moveCenter(bbox.minx + worldWidth / 2, bbox.miny + worldHeight / 2);
        viewport.name = 'viewport';
        this.app.stage.addChild(viewport);
        if (!this.opts.disableMovement) {
            viewport.drag(this.dragOpts).pinch(this.pinchOpts);
        }
        if (this.opts.clampOpts) {
            this.viewport.clamp({
                right: bbox.maxx + worldWidth,
                left: bbox.minx - worldWidth,
                bottom: bbox.miny - worldHeight,
                top: bbox.maxy + worldHeight,
                ...this.opts.clampOpts,
            });
        }
        if (this.opts.enableClamp) {
            const ratio = this.opts.zoomClampRatio || 2;
            if (this.opts.zoomClampOpts) {
                viewport.clampZoom(this.opts.zoomClampOpts);
            }
            else if (worldHeight > worldWidth) {
                viewport.clampZoom({ maxHeight: ratio * viewport.worldHeight });
            }
            else {
                viewport.clampZoom({ maxWidth: ratio * viewport.worldWidth });
            }
        }
        if (!this.opts.disableWheel) {
            viewport.wheel({
                center: null,
                ...this.wheelOpts,
            });
        }
        if (!this.opts.disableBounce) {
            const bounceExpandPercent = typeof this.opts.bounceExpandPercent === 'number' ? this.opts.bounceExpandPercent + 1 : 1.2;
            const expand = [worldWidth * bounceExpandPercent, worldHeight * bounceExpandPercent];
            viewport.bounce({
                bounceBox: new PIXI.Rectangle(bbox.minx - expand[0], bbox.miny - expand[1], worldWidth + 2 * expand[0], worldHeight + 2 * expand[1]),
                sides: 'all',
                underflow: 'center',
                ...this.bounceOpts,
            });
        }
        // Set up event relaying from viewport to ModelRenderer
        this.setupViewportEventRelaying(viewport);
        return viewport;
    }
    setupViewportEventRelaying(viewport) {
        // Relay viewport drag events
        viewport.on('drag-start', (event) => {
            this.emit('viewportDragStart', event);
        });
        viewport.on('drag-end', (event) => {
            this.emit('viewportDragEnd', event);
        });
        // Relay viewport move events
        viewport.on('moved', (event) => {
            this.emit('viewportMoved', event);
        });
        // Relay viewport zoom events
        viewport.on('zoomed', (event) => {
            this.emit('viewportZoomed', event);
        });
        // Relay other common viewport events
        viewport.on('wheel', (event) => {
            this.emit('viewportWheel', event);
        });
        viewport.on('pinch-start', (event) => {
            this.emit('viewportPinchStart', event);
        });
        viewport.on('pinch-end', (event) => {
            this.emit('viewportPinchEnd', event);
        });
    }
    on(event, listener) {
        return super.on(event, listener);
    }
    handleResized = ([node]) => {
        const width = node.contentBoxSize[0].inlineSize;
        const height = node.contentBoxSize[0].blockSize;
        if (width === 0 || height === 0) {
            return; // Zero dimensions can happen if the canvas is hidden (e.g., with `display: none`)
        }
        this.app.renderer.resize(width, height);
        // Resize the pixi viewport renderer
        this.viewport?.resize(width, height, this.viewport.worldWidth, this.viewport.worldHeight);
        if (this.opts.fitOnResize) {
            this.viewport?.fit();
        }
    };
    /**
     * Subscribes to the next change of `devicePixelRatio`, which happens when the browser zoom changes or
     * the window moves to a display with a different pixel density. A resolution media query only matches
     * one exact ratio, so it is replaced after every change.
     */
    watchResolution() {
        this.resolutionQuery?.removeEventListener('change', this.handleResolutionChanged);
        this.resolutionQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
        this.resolutionQuery.addEventListener('change', this.handleResolutionChanged);
    }
    handleResolutionChanged = () => {
        this.watchResolution();
        this.setResolution(window.devicePixelRatio);
    };
    /**
     * Re-allocates the canvas at a new device pixel ratio while keeping its CSS size, so the drawing stays
     * sharp on screen. Emits `resolutionChanged` when the ratio actually changed.
     * @param resolution - Device pixels per CSS pixel.
     */
    setResolution(resolution) {
        if (!this.app || this.app.renderer.resolution === resolution) {
            return;
        }
        const { renderer } = this.app;
        renderer.resolution = resolution;
        renderer.resize(renderer.screen.width, renderer.screen.height);
        const interactionManager = this.getInteractionManager();
        if (interactionManager) {
            interactionManager.resolution = resolution;
        }
        this.emit('resolutionChanged', resolution);
        this.app.render();
    }
    getInteractionManager() {
        return this.app.renderer.plugins.interaction;
    }
    getInitialZoombox() {
        return this.getWorldScaledBbox(this.initialZoombox);
    }
    getBbox() {
        return this.getWorldScaledBbox(this.modelBbox);
    }
    getWorldScaledBbox(bbox) {
        if (!bbox ||
            !Number.isFinite(bbox.minx) ||
            !Number.isFinite(bbox.maxx) ||
            !Number.isFinite(bbox.miny) ||
            !Number.isFinite(bbox.maxy)) {
            console.error('GingerSnap_getWorldScaledBbox_InvalidBox', { bbox });
            throw new Error('Boundary box invalid. Could not render board');
        }
        return {
            minx: bbox.minx * ConstantsExports.WORLD_SCALE,
            maxx: bbox.maxx * ConstantsExports.WORLD_SCALE,
            miny: bbox.miny * ConstantsExports.WORLD_SCALE,
            maxy: bbox.maxy * ConstantsExports.WORLD_SCALE,
        };
    }
    getBoundsBbox() {
        const bbox = this.getBbox();
        const worldWidth = bbox.maxx - bbox.minx;
        const worldHeight = bbox.maxy - bbox.miny;
        const bounceExpandPercent = typeof this.opts.bounceExpandPercent === 'number' ? this.opts.bounceExpandPercent + 1 : 1.2;
        const expand = [worldWidth * bounceExpandPercent, worldHeight * bounceExpandPercent];
        return {
            minx: bbox.minx - expand[0],
            miny: bbox.miny - expand[1],
            maxx: bbox.minx + worldWidth + expand[0],
            maxy: bbox.miny + worldHeight + expand[1],
        };
    }
}

/**
 * A container that assigns its tint to its children. Note that tint
 * is only applied to existing children, not new children added after adding tint.
 * Given current usage this is fine but in the future may want to implement addChild().
 */
class TintedContainer extends PIXI.Container {
    _tint = 0xffffff;
    get tint() {
        return this._tint;
    }
    set tint(value) {
        for (const child of this.children) {
            const tChild = child;
            tChild.tint = value;
        }
        this._tint = value;
    }
}

/** Splits a run into a sequence of word + whitespace tokens for
 *  greedy line-wrap layout. Words and runs of whitespace are kept
 *  separate so the layout can drop leading whitespace at line
 *  boundaries without losing inter-word spacing mid-line. */
function tokenizeRun(value) {
    const tokens = [];
    let i = 0;
    while (i < value.length) {
        const ch = value[i];
        const isSpace = /\s/.test(ch);
        let j = i + 1;
        while (j < value.length && /\s/.test(value[j]) === isSpace) {
            j++;
        }
        tokens.push(value.slice(i, j));
        i = j;
    }
    return tokens;
}
/** Returns the largest `sizePt` among a paragraph's runs, falling back
 *  to `defaultPt` for runs without a per-run override. Used to size the
 *  vertical step between wrapped lines so a mid-paragraph size override
 *  doesn't cause adjacent runs to overlap. */
function paragraphLineHeight(paragraph, defaultPt) {
    let max = defaultPt;
    for (const run of paragraph.runs) {
        const sz = run.sizePt ?? defaultPt;
        if (sz > max) {
            max = sz;
        }
    }
    return max;
}

var distExports = requireDist$1();

/** Determines whether legacy OrCAD pin-name geometry compensation applies. @param model - Model provenance and text-contract version. @returns Whether the compensation may decode and reposition a pin name. */
function shouldCompensateOrcadPinName(model) {
    return !distExports.isModelVersionAtLeast(model, distExports$1.ModelText.MODEL_TEXT_MIN_VERSION) && model.type === 'orcad';
}
/** Applies a legacy CAD-tool display adjustment when the model predates parser-authored text transforms. @param model - Model provenance and semantic version. @param text - Raw text object. @param pinStart - Candidate pin hotpoint. @returns Adjusted text for the matching legacy tool, or the original text. */
function getLegacyPinNameForRendering(model, text, pinStart) {
    return shouldCompensateOrcadPinName(model) ? distExports$1.Parse.OrCAD.getOrcadPinNameForRendering(text, pinStart) : text;
}
/**
 * Resolves sequential replacements and legacy dialect markers on a local text copy.
 *
 * @param model - Model provenance and text-contract version.
 * @param source - Model-owned text object.
 * @returns Local display text and bars indexed into its final value.
 */
function resolveTextForRendering(model, source) {
    let value = source.value.toString();
    const barTransforms = [];
    for (const transform of source.textTransforms ?? []) {
        switch (transform.type) {
            case 'replace':
                value = [
                    transform.start > 0 ? value.substring(0, transform.start) : '',
                    transform.value,
                    value.substring(transform.start + transform.length),
                ].join('');
                break;
            case 'bar':
                barTransforms.push(transform);
                break;
        }
    }
    if (!distExports.isModelVersionAtLeast(model, distExports$1.ModelText.MODEL_TEXT_MIN_VERSION)) {
        const display = distExports$1.ModelText.resolveDisplayText({
            raw: value,
            dialects: [distExports$1.ModelText.getTextDialectForCadTool(model.type)],
        });
        value = display.text;
        barTransforms.push(...display.transforms.filter((transform) => transform.type === 'bar'));
    }
    return { text: { ...source, value }, barTransforms };
}

/** Detects whether a design is a PCB or a schematic and renders the appropriate type. */
function RenderDrawingToManager(manager, model, design, opts = {}) {
    return FullModelRenderer.RenderDrawingToManager(manager, model, design, opts);
}
/** Renders a PCB design to a given {@link DrawingManager}. */
function RenderBoardToManager(manager, model, design, opts = {}) {
    return FullModelRenderer.RenderBoardToManager(manager, model, design, opts);
}
/** Renders a schematic design to a given {@link DrawingManager}. */
function RenderSchematicToManager(manager, model, design, opts = {}) {
    return FullModelRenderer.RenderSchematicToManager(manager, model, design, opts);
}
/** Draws a dashed/dotted rectangle outline by walking each of the four
 *  edges with alternating stroke + gap segments. Pixi v6 has no native
 *  dash support, so we approximate by emitting `moveTo` / `lineTo`
 *  per dash chunk. Dash length is scaled to the rectangle's perimeter
 *  so small rectangles still show distinct dashes. */
function drawDashedRect(g, x, y, w, h, style) {
    const corners = [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
    ];
    const perimeter = 2 * (w + h);
    const dashLen = style === 'dot' ? Math.max(2, perimeter / 200) : Math.max(4, perimeter / 80);
    const gapLen = style === 'dot' ? Math.max(2, dashLen) : Math.max(3, dashLen * 0.6);
    let drawing = true;
    let remaining = dashLen;
    let cursor = [corners[0][0], corners[0][1]];
    g.moveTo(cursor[0], cursor[1]);
    for (let i = 1; i <= corners.length; i++) {
        const target = corners[i % corners.length];
        while (true) {
            const dx = target[0] - cursor[0];
            const dy = target[1] - cursor[1];
            const segLen = Math.sqrt(dx * dx + dy * dy);
            if (segLen <= 0) {
                break;
            }
            if (remaining >= segLen) {
                if (drawing) {
                    g.lineTo(target[0], target[1]);
                }
                else {
                    g.moveTo(target[0], target[1]);
                }
                cursor = [target[0], target[1]];
                remaining -= segLen;
                break;
            }
            const t = remaining / segLen;
            const nx = cursor[0] + dx * t;
            const ny = cursor[1] + dy * t;
            if (drawing) {
                g.lineTo(nx, ny);
            }
            else {
                g.moveTo(nx, ny);
            }
            cursor = [nx, ny];
            drawing = !drawing;
            remaining = drawing ? dashLen : gapLen;
        }
    }
}
class FullModelRenderer {
    manager;
    model;
    design;
    opts;
    allowMissingSourceLayers = false;
    layerMap = {};
    get generateOpts() {
        return this.opts.generateOpts;
    }
    get disableHitboxes() {
        return !!this.opts.disableHitboxes;
    }
    /**
     * Can reduce this threshold to improve UI responsiveness. Reduces accuracy of hitboxes on complex shapes.
     */
    get texturizePolygonThreshold() {
        if (typeof this.opts.texturizePolygonThreshold === 'number') {
            return this.opts.texturizePolygonThreshold;
        }
        return ConstantsExports.DEFAULT_TEXTURIZE_POLYGON_THRESHOLD;
    }
    get skipLargePolygonThreshold() {
        if (typeof this.opts.skipLargePolygonThreshold === 'number' && !Number.isNaN(this.opts.skipLargePolygonThreshold)) {
            return this.opts.skipLargePolygonThreshold;
        }
        return ConstantsExports.DEFAULT_SKIP_LARGE_POLYGONS_THRESHOLD;
    }
    constructor(manager, model, design, opts) {
        this.manager = manager;
        this.model = model;
        this.design = design;
        this.opts = opts;
    }
    static RenderDrawingToManager(manager, model, design, opts = {}) {
        switch (design.modelType) {
            case 'pcb':
                return RenderBoardToManager(manager, model, design, opts);
            case 'schematic':
                return RenderSchematicToManager(manager, model, design, opts);
            default:
                // @ts-expect-error: design should be never if it's not pcb or schematic.
                throw new Error(`Unsupported model type: ${design.modelType}`);
        }
    }
    static RenderBoardToManager(manager, model, design, opts = {}) {
        const renderer = new FullModelRenderer(manager, model, design, opts);
        if (!design) {
            // @ts-expect-error: design should be never if it's not defined.
            throw new Error(`Could not find design id '${design.designId}' in project`);
        }
        else if (design.modelType !== 'pcb') {
            throw new Error(`Design '${design.designId}' is not a pcb`);
        }
        const layers = model.layerSets[design.layerSet];
        const visibleLayers = layers.filter((l) => l.defaultVisible).map((l) => l.id);
        manager.setDefaultVisibleLayers(visibleLayers);
        layers.forEach((layer) => {
            renderer.layerMap[layer.id] = layer;
            design.zones[layer.id]?.forEach((zone) => renderer.drawZone(zone, layer.id));
            design.tracks[layer.id]?.forEach((track) => {
                const color = manager.colorProvider.getColorForLayer(layer.id);
                const de = new DrawingElement(manager, track, 'tracks', ObjectSelectionType.Net);
                de.addGraphic(layer.id, renderer.renderTrack(track), color, renderer.withTypeProp(track, layer.id, renderer.netProperty(track)));
                manager.addElement(de);
            });
        });
        design.modules.forEach((module) => {
            const component = model.components[module.componentRef];
            if (!component) {
                throw new Error(`Could not find component ${module.componentRef} in project`);
            }
            const fp = component.footprints.find((f) => f.ref === module.footprintRef);
            if (!fp) {
                throw new Error(`Could not find footprint ${module.footprintRef} in component ${module.componentRef}`);
            }
            renderer.renderFootprint(module, fp, design.throughHoleLayers);
        });
        design.vias?.forEach((via) => renderer.drawVia(via));
        renderer.renderEdges(layers, design);
        renderer.renderModelDrawings(design);
        manager.setDrawOverlayFunc(renderer.drawOverlay);
        if (renderer.opts.drawBoundingBox) {
            renderer.drawBoundingBox(design);
        }
    }
    static RenderSchematicToManager(manager, model, design, opts = {}) {
        const renderer = new FullModelRenderer(manager, model, design, opts);
        if (!design) {
            // @ts-expect-error: design should be never if it's not defined.
            throw new Error(`Could not find design id '${design.designId}' in project`);
        }
        else if (design.modelType !== 'schematic') {
            throw new Error(`Design '${design.designId}' is not a schematic`);
        }
        const visibleLayers = [];
        const layers = model.layerSets[design.layerSet];
        layers.forEach((layer) => {
            renderer.layerMap[layer.id] = layer;
            if (layer.defaultVisible) {
                visibleLayers.push(layer.id);
            }
        });
        manager.setVisibleLayers(visibleLayers);
        // Render modules with multi-instance awareness
        const multiConfig = design.multiInstanceConfiguration;
        const selectedInstance = opts.multiInstanceId;
        if (multiConfig) {
            // Always render shared modules (those not tied to a specific instance)
            design.modules.filter((m) => !m.multiInstance).forEach((module) => renderer.renderSchematicModule(module));
            if (selectedInstance) {
                // Instance mode: render only modules belonging to the selected instance
                design.modules
                    .filter((m) => m.multiInstance?.instanceId === selectedInstance)
                    .forEach((module) => renderer.renderSchematicModule(module));
            }
            else {
                // Overview mode: render display modules from multi-instance configuration
                multiConfig.multiInstanceModules.forEach((module) => renderer.renderSchematicModule(module));
            }
        }
        else {
            // Non-multi-instance: render all modules
            design.modules.forEach((module) => renderer.renderSchematicModule(module));
        }
        renderer.renderModelDrawings(design);
        manager.setDrawOverlayFunc(renderer.drawOverlay);
        if (renderer.opts.drawBoundingBox) {
            renderer.drawBoundingBox(design);
        }
    }
    drawBoundingBox(design) {
        this.manager.addOverlay({
            id: ConstantsExports.TAG_BBOX,
            annotations: [],
            drawing: [
                {
                    type: 'rect',
                    strokeWidth: 1,
                    start: [design.edges_bbox.minx, design.edges_bbox.miny],
                    end: [design.edges_bbox.maxx, design.edges_bbox.maxy],
                },
            ],
            tags: [{ tag: ConstantsExports.TAG_BBOX, defaultVisible: false }],
        });
    }
    mapFaceLayer(sourceLayer, renderFace) {
        const layer = this.layerMap[sourceLayer];
        if (!layer) {
            if (!this.allowMissingSourceLayers) {
                console.warn(`Missing source layer ${sourceLayer}`);
            }
            return sourceLayer;
        }
        if (layer.face) {
            if (typeof layer.faceMirrorLayer === 'undefined') {
                this.manager.addRenderWarning(`Missing layer face mirror for layer ${sourceLayer}`);
            }
            if (layer.face !== renderFace) {
                return layer.faceMirrorLayer;
            }
        }
        return sourceLayer;
    }
    renderEdges(layers, model) {
        const edgeLayers = layers.filter((l) => l.type === DesignModelsExports.PcbLayerType.Edge);
        if (edgeLayers.length === 0) {
            this.manager.addRenderWarning('No edge layers found. Skipping drawering edges');
            return;
        }
        else if (edgeLayers.length > 1) {
            this.manager.addRenderWarning('Multiple edge layers found. Using first found: ' + edgeLayers[0].name);
        }
        this.renderDrawings(model.edges, edgeLayers[0].id);
    }
    drawOverlay = (overlay, defaultLayer) => {
        const tags = overlay.tags || [{ tag: ConstantsExports.TAG_OVERLAY, defaultVisible: false }];
        const elements = [];
        // Create `CommentOverlay` if there is a comment
        let co;
        if (overlay.comment) {
            co = new CommentOverlay(this.manager, overlay.comment, defaultLayer, tags);
            co.render();
            elements.push(co);
        }
        if (overlay.drawing) {
            const de = new DrawingElement(this.manager, overlay.drawing || {}, 'drawings', ObjectSelectionType.None);
            let element;
            if (overlay.drawing) {
                for (const drawing of graphicsOverlayDrawing(overlay.drawing, defaultLayer)) {
                    for (const drawingLayer of drawing.layers) {
                        const renderedElement = this.renderDrawingElement(drawing.drawing, tags, drawingLayer);
                        if (!renderedElement) {
                            continue;
                        }
                        for (const { g, layer } of renderedElement) {
                            const drawingColor = this.manager.colorProvider.getColorForLayer(layer);
                            de.addGraphic(layer, g, drawingColor, {
                                tags,
                            });
                        }
                    }
                }
                element = de.container;
            }
            if (overlay.annotations) {
                const bounds = element.getBounds();
                const annotiations = this.renderOverlayAnnotations(bounds, overlay.annotations, defaultLayer, tags);
                const annotationColor = getDefaultColors()['OVERLAY_ANNOTATIONS'];
                for (const annotation of annotiations) {
                    de.addGraphic(defaultLayer + (annotation.isMask ? 1 : 0), annotation.graphic, annotationColor, {
                        tags,
                    });
                }
            }
            this.manager.addElement(de);
            elements.push(de);
            if (co) {
                co.addControlledChild(de);
            }
        }
        return elements;
    };
    renderModelDrawings(model) {
        for (const [layerId, drawings] of Object.entries(model.drawings)) {
            this.renderDrawings(drawings, assertInteger(layerId));
        }
    }
    renderDrawings(drawings, defaultLayer, tags) {
        if (typeof defaultLayer !== 'number') {
            this.manager.addRenderWarning('Missing layer while rendering drawings');
            return [];
        }
        if (!drawings) {
            this.manager.addRenderWarning(`Empty drawings object for layer ${defaultLayer}`);
            return [];
        }
        const selectedInstance = this.opts.multiInstanceId;
        const elements = [];
        drawings.forEach((item) => {
            // In instance mode, skip drawings that belong to other instances
            const drawingBase = item;
            if (selectedInstance && drawingBase.nets?.length && !drawingBase.nets.some((n) => n.i === selectedInstance)) {
                return;
            }
            const net = this.netProperty(drawingBase);
            const de = new DrawingElement(this.manager, item, 'drawings', net ? ObjectSelectionType.Net : ObjectSelectionType.None);
            let deUsed = false;
            if (item.type === 'zone') {
                // Zones are special!
                deUsed = this.renderZone(defaultLayer, item, de, tags);
            }
            else {
                const gs = this.renderDrawingElement(item, tags, defaultLayer);
                for (const { g, layer, colorOverride, contrastWithLayer } of gs) {
                    let color = this.manager.colorProvider.getColorForLayer(defaultLayer);
                    if (colorOverride) {
                        color = colorToGraphicsOpts(colorOverride.color, colorOverride.alpha);
                    }
                    let settings = this.withTypeProp(item, layer, {
                        tags,
                        properties: net?.properties,
                    });
                    if (contrastWithLayer) {
                        settings = { ...(settings || {}), contrastWithLayer };
                    }
                    de.addGraphic(layer, g, color, settings);
                    if (item.type === 'text') {
                        this.addTextBoundsToDrawingEelment(de, layer, g, color, net?.properties);
                    }
                    deUsed = true;
                }
            }
            if (deUsed) {
                this.manager.addElement(de);
                elements.push(de);
            }
        });
        return elements;
    }
    /** Creates {@link GraphicsElement}s for the given drawing. */
    renderDrawingElement(item, tags, defaultLayer) {
        let g;
        const results = [];
        let colorOverride;
        switch (item.type) {
            case 'segment':
                g = this.renderSegment(item);
                break;
            case 'polygon':
                g = this.renderPolygon(item);
                break;
            case 'track':
                g = this.renderTrack(item);
                break;
            case 'bus':
                g = this.renderBus(item);
                break;
            case 'zone':
                this.drawZone(item, item.layer, tags);
                break;
            case 'circle':
                g = this.renderCircle(item);
                break;
            case 'junction':
                g = this.renderJunction(item);
                break;
            case 'text':
                g = this.renderText(item);
                break;
            case 'textbox':
                results.push(...this.renderTextBox(item, defaultLayer));
                break;
            case 'rect':
                g = this.renderRectangle(item, undefined);
                break;
            case 'label':
                g = this.renderLabel(item);
                break;
            case 'arc':
                g = this.renderArc(item);
                break;
            case 'curve':
                g = this.renderCurve(item);
                break;
            case 'schref':
                this.renderSchematicReference(item);
                break;
            case 'group':
                results.push(...this.renderDrawingGroup(item, defaultLayer, tags));
                break;
            case 'via':
                g = this.renderViaLayer(item);
                break;
            case 'image':
                g = this.renderImage(item);
                break;
            case 'rich-text':
                g = this.renderRichText(item);
                break;
            default:
                this.manager.addRenderWarning(`Drawing type '${item.type}' cannot be rendered yet.`);
        }
        if ('colorOverride' in item) {
            colorOverride = this.colorOverrideToColor(item.colorOverride, undefined);
        }
        if (g) {
            results.push({
                g,
                layer: defaultLayer,
                colorOverride,
                contrastWithLayer: item.type === 'text' ? item.contrastWithLayer : undefined,
            });
        }
        return results;
    }
    renderCurve(curve, module) {
        const g = new PIXI.Graphics();
        const scaleFactor = ConstantsExports.WORLD_SCALE * ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
        const width = scaleFactor * max(curve.width, ConstantsExports.SEGMENT_MIN_WIDTH);
        g.lineStyle({
            width: width,
            color: ConstantsExports.COLOR_UNFILLED,
            cap: PIXI.LINE_CAP.ROUND,
        });
        // Scale all points
        const startScaled = [curve.start[0] * scaleFactor, curve.start[1] * scaleFactor];
        const cpaScaled = [curve.cpa[0] * scaleFactor, curve.cpa[1] * scaleFactor];
        const cpbScaled = [curve.cpb[0] * scaleFactor, curve.cpb[1] * scaleFactor];
        const endScaled = [curve.end[0] * scaleFactor, curve.end[1] * scaleFactor];
        // Move to start point and draw cubic Bezier curve
        g.moveTo(startScaled[0], startScaled[1]);
        g.bezierCurveTo(cpaScaled[0], cpaScaled[1], cpbScaled[0], cpbScaled[1], endScaled[0], endScaled[1]);
        g.angle = -(module?.transform.angle || 0);
        g.scale.set(ConstantsExports.WORLD_SCALE / scaleFactor);
        // For hitbox, we'll approximate the curve with a polygon
        // Sample points along the Bezier curve using the parametric equation
        if (!this.disableHitboxes) {
            const hitboxPoints = [];
            const numSamples = 20; // Number of points to sample along the curve
            for (let i = 0; i <= numSamples; i++) {
                const t = i / numSamples;
                const oneMinusT = 1 - t;
                // Cubic Bezier formula: B(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
                const x = oneMinusT * oneMinusT * oneMinusT * curve.start[0] +
                    3 * oneMinusT * oneMinusT * t * curve.cpa[0] +
                    3 * oneMinusT * t * t * curve.cpb[0] +
                    t * t * t * curve.end[0];
                const y = oneMinusT * oneMinusT * oneMinusT * curve.start[1] +
                    3 * oneMinusT * oneMinusT * t * curve.cpa[1] +
                    3 * oneMinusT * t * t * curve.cpb[1] +
                    t * t * t * curve.end[1];
                hitboxPoints.push([x, y]);
            }
            g.hitArea = new PIXI.Polygon(hitboxPoints.flatMap((p) => [p[0] * ConstantsExports.WORLD_SCALE, p[1] * ConstantsExports.WORLD_SCALE]));
        }
        let addEl = g;
        if (module?.transform.mirrored[0] || module?.transform.mirrored[1]) {
            addEl = new TintedContainer();
            addEl.addChild(g);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        const center = TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]);
        addEl.position.x = ConstantsExports.WORLD_SCALE * (center[0] + (module?.center[0] || 0));
        addEl.position.y = ConstantsExports.WORLD_SCALE * (center[1] + (module?.center[1] || 0));
        return addEl;
    }
    renderArc(arc, module) {
        const g = new PIXI.Graphics();
        const scaleFactor = ConstantsExports.WORLD_SCALE * ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
        const width = scaleFactor * max(arc.lineWidth, ConstantsExports.SEGMENT_MIN_WIDTH);
        g.lineStyle({
            width: width,
            color: ConstantsExports.COLOR_UNFILLED,
            cap: PIXI.LINE_CAP.ROUND,
        });
        const radiusScaled = arc.radius * scaleFactor;
        const centerScaled = [arc.center[0] * scaleFactor, arc.center[1] * scaleFactor];
        // NOTE: Arcs for module are drawn counterclockwise, otherwise clockwise
        g.arc(centerScaled[0], centerScaled[1], radiusScaled, UtilsExports.degreesToRadians(arc.startAngle), UtilsExports.degreesToRadians(arc.endAngle), false);
        g.angle = -(module?.transform.angle || 0);
        g.scale.set(ConstantsExports.WORLD_SCALE / scaleFactor);
        // Generate hitbox polygon for the arc - do this by creating a polygon with vertices
        // ever ARC_HITBOX_POLYGON_VERTEX_DEGREES and rounded edges to approximate the shape
        // of the arc in a way that can be used for hitbox calculation.
        if (!this.disableHitboxes) {
            let startAngle = arc.endAngle;
            const endAngle = arc.startAngle;
            if (endAngle < startAngle) {
                startAngle -= 360;
            }
            const deltaWidth = (width / 2) * 1.05;
            const radiuses = [radiusScaled - deltaWidth, radiusScaled + deltaWidth];
            const minPoints = [];
            const maxPoints = [];
            for (let i = startAngle + 1; i < endAngle; i += ConstantsExports.ARC_HITBOX_POLYGON_VERTEX_DEGREES) {
                const minPoint = TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[0], 0], i), centerScaled);
                minPoints.push({ x: minPoint[0], y: minPoint[1] });
                const maxPoint = TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[1], 0], i), centerScaled);
                maxPoints.push({ x: maxPoint[0], y: maxPoint[1] });
            }
            const endDeltaDeg = UtilsExports.radiansToDegrees(deltaWidth / radiusScaled);
            const startPoints = [
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[1], 0], startAngle), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[1], 0], startAngle - (endDeltaDeg * 1) / 2), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiusScaled, 0], startAngle - endDeltaDeg), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[0], 0], startAngle - (endDeltaDeg * 1) / 2), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[0], 0], startAngle), centerScaled),
            ];
            const endPoints = [
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[0], 0], endAngle), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[0], 0], endAngle + (endDeltaDeg * 1) / 2), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiusScaled, 0], endAngle + endDeltaDeg), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[1], 0], endAngle + (endDeltaDeg * 1) / 2), centerScaled),
                TransformExports.translatePoint(TransformExports.rotatePoint([radiuses[1], 0], endAngle), centerScaled),
            ];
            const hitboxPolygon = new PIXI.Polygon([
                ...startPoints.map((xy) => this.mapXyToPointData(xy)),
                ...minPoints,
                ...endPoints.map((xy) => this.mapXyToPointData(xy)),
                ...maxPoints.reverse(),
            ]);
            g.hitArea = hitboxPolygon;
        }
        let addEl = g;
        if (module?.transform.mirrored[0] || module?.transform.mirrored[1]) {
            addEl = new TintedContainer();
            addEl.addChild(g);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        const center = TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]);
        addEl.position.x = ConstantsExports.WORLD_SCALE * (center[0] + (module?.center[0] || 0));
        addEl.position.y = ConstantsExports.WORLD_SCALE * (center[1] + (module?.center[1] || 0));
        return addEl;
    }
    mapXyToPointData(pos) {
        return {
            x: pos[0],
            y: pos[1],
        };
    }
    renderDrawingGroup(group, defaultLayer, tags) {
        const x = ConstantsExports.WORLD_SCALE * group.pos[0];
        const y = ConstantsExports.WORLD_SCALE * group.pos[1];
        const angle = -(group.angle || 0);
        return group.drawings.flatMap((drawing) => {
            const gs = this.renderDrawingElement(drawing, tags, defaultLayer);
            for (const { g } of gs) {
                g.x += x;
                g.y += y;
                g.angle += angle;
            }
            return gs;
        });
    }
    renderTrack(track) {
        return this.renderSegment(track);
    }
    renderBus(bus) {
        return this.renderSegment(bus);
    }
    drawZone(zone, layer, tags) {
        const de = new DrawingElement(this.manager, zone, 'zones', ObjectSelectionType.Net, {
            properties: {
                [DrawingProperties.Net]: zone.net,
            },
        });
        if (this.renderZone(layer, zone, de, tags)) {
            this.manager.addElement(de);
        }
    }
    renderZone(layer, zone, de, tags) {
        const numVertices = zone.polygons.reduce((sum, p) => sum + p.points.length, 0);
        if (numVertices >= this.skipLargePolygonThreshold) {
            this.manager.addRenderWarning(`[PERF] Skipping rendering of large zone on net ${zone.net} with ${numVertices} vertices`);
            return false;
        }
        let g = new PIXI.Graphics();
        const color = this.manager.colorProvider.getColorForLayer(layer);
        zone.polygons.forEach((p) => {
            this.renderZonePolygon(p, g, false);
            let zp = this.renderZonePolygon(p, undefined, true);
            if (p.points.length >= this.texturizePolygonThreshold) {
                zp = this.texturizeGraphic(zp);
            }
            de.addGraphic(layer, zp, color, {
                tags: [
                    ...(tags || []),
                    {
                        tag: ConstantsExports.TAG_POLYGON_FILL,
                        defaultVisible: !!p.filled,
                    },
                ],
                properties: {
                    [DrawingProperties.Net]: zone.net,
                },
            });
        });
        if (numVertices >= this.texturizePolygonThreshold) {
            g = this.texturizeGraphic(g);
        }
        de.addGraphic(layer, g, color, this.withTypeProp(zone, layer, {
            tags,
            properties: {
                [DrawingProperties.Net]: zone.net,
            },
        }));
        return true;
    }
    renderZonePolygon(p, g, filled) {
        let myGraphic = false;
        if (!g) {
            myGraphic = true;
            g = new PIXI.Graphics();
            g.clear();
        }
        if (filled) {
            g.beginFill(ConstantsExports.COLOR_UNFILLED, 0.5);
            g.lineStyle();
        }
        else {
            g.lineStyle({
                color: ConstantsExports.COLOR_UNFILLED,
                width: ConstantsExports.WORLD_SCALE * (typeof p.lineWidth === 'number' ? p.lineWidth : ConstantsExports.SEGMENT_MIN_WIDTH),
            });
        }
        const hitPolyPoints = [];
        for (let i = 0; i < p.points.length; i++) {
            const point = {
                x: ConstantsExports.WORLD_SCALE * p.points[i][0],
                y: ConstantsExports.WORLD_SCALE * p.points[i][1],
            };
            if (i === 0) {
                g.moveTo(point.x, point.y);
            }
            else {
                g.lineTo(point.x, point.y);
            }
            hitPolyPoints.push(point);
        }
        if (filled) {
            g.endFill();
        }
        if (myGraphic && p.points.length >= this.texturizePolygonThreshold) {
            return this.texturizeGraphic(g);
        }
        g.hitArea = new PIXI.Polygon(hitPolyPoints);
        return g;
    }
    texturizeGraphic(g) {
        const region = g.getLocalBounds(new PIXI.Rectangle());
        const sprite = new PIXI.Sprite(this.manager.app.renderer.generateTexture(g, {
            resolution: ConstantsExports.TEXTURIZED_ELEMENT_SCALE / 10,
        }));
        sprite.x = region.x;
        sprite.y = region.y;
        return sprite;
    }
    drawVia(via) {
        const de = new DrawingElement(this.manager, via, 'vias', ObjectSelectionType.Net);
        this.renderVia(via, de);
        this.manager.addElement(de);
    }
    renderVia(via, de) {
        for (const layer of via.layers) {
            const color = this.manager.colorProvider.getColorForLayer(layer);
            const g = this.renderViaLayer(via);
            if (g) {
                de.addGraphic(layer, g, color, this.withTypeProp(via, layer, this.netProperty(via)));
            }
        }
    }
    renderViaLayer(via) {
        const g = new PIXI.Graphics();
        if (via.shape === 'circle') {
            g.beginFill(ConstantsExports.COLOR_UNFILLED);
            g.drawCircle(0, 0, (ConstantsExports.TEXTURIZED_ELEMENT_SCALE * via.diameter) / 2);
            g.endFill();
            g.scale.set(ConstantsExports.WORLD_SCALE / ConstantsExports.TEXTURIZED_ELEMENT_SCALE, ConstantsExports.WORLD_SCALE / ConstantsExports.TEXTURIZED_ELEMENT_SCALE);
        }
        else if (via.shape === 'rect') {
            g.beginFill(ConstantsExports.COLOR_UNFILLED);
            g.drawRect((-ConstantsExports.WORLD_SCALE * via.diameter) / 2, 4 - (ConstantsExports.WORLD_SCALE * via.diameter) / 2, ConstantsExports.WORLD_SCALE * via.diameter, ConstantsExports.WORLD_SCALE * via.diameter);
            g.endFill();
        }
        else if (via.shape === 'custom') {
            if (!via.polygons) {
                this.manager.addRenderWarning(`Custom shape via on net '${via.net}' was missing polygon property`);
                return;
            }
            via.polygons.forEach((path) => {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.moveTo(ConstantsExports.WORLD_SCALE * path[0][0], ConstantsExports.WORLD_SCALE * path[0][1]);
                for (let i = 1; i < path.length; i++) {
                    g.lineTo(ConstantsExports.WORLD_SCALE * path[i][0], ConstantsExports.WORLD_SCALE * path[i][1]);
                }
                g.endFill();
            });
        }
        else {
            // todo
            this.manager.addRenderWarning(`Drawing of via shape '${via.shape}' has not been implemented.`);
            return;
        }
        if (via.drillDiameter) {
            const scale = via.shape === 'circle' ? ConstantsExports.TEXTURIZED_ELEMENT_SCALE : ConstantsExports.WORLD_SCALE;
            g.beginHole();
            g.drawCircle(0, 0, (scale * via.drillDiameter) / 2);
            g.endHole();
        }
        g.position.x = ConstantsExports.WORLD_SCALE * via.center[0];
        g.position.y = ConstantsExports.WORLD_SCALE * via.center[1];
        return g;
    }
    renderFootprint(module, footprint, throughHoleLayers, de) {
        if (!this.model.modules) {
            console.error('Project modules missing: ', this.model);
        }
        const metaModule = this.model.modules?.find((m) => m.name === module.ref);
        const renderedProps = new Set();
        const myDe = !de;
        if (!de) {
            de = new DrawingElement(this.manager, module, 'modules', ObjectSelectionType.Object, {
                properties: {
                    [DrawingProperties.ObjectId]: module.name,
                    [DrawingProperties.Component]: module.componentRef,
                    [DrawingProperties.Value]: metaModule?.value,
                },
                name: `${module.name} DrawingElement`,
            });
        }
        if (footprint.pads) {
            footprint.pads.map((pad) => this.renderPad(module, footprint, pad, de, throughHoleLayers));
        }
        if (footprint.pins) {
            footprint.pins.map((pin) => this.renderFootprintPin(pin, module, footprint, de, renderedProps));
        }
        this.renderFootprintDrawings(footprint, footprint.drawings, module, de, renderedProps);
        for (const [key, prop] of Object.entries(module.drawProperties)) {
            // Already rendered
            if (renderedProps.has(key) || typeof prop.layer !== 'number' || prop.hidden) {
                continue;
            }
            const text = {
                attr: [],
                size: 1.27,
                mirror: module.transform.mirrored,
                ...prop,
                type: 'text',
            };
            // Honor per-property `colorOverride` (used to recolor instance
            // labels — e.g. an Allegro placement marked with a red tint
            // row stamps the same red on every drawProperties entry).
            const color = this.colorForFootprintDrawing(prop.layer, prop, module);
            const g = this.renderText(text);
            if (g) {
                de.addGraphic(prop.layer, g, color);
                this.addTextBoundsToDrawingEelment(de, prop.layer, g, color);
            }
        }
        if (myDe) {
            this.manager.addElement(de);
        }
    }
    renderFootprintDrawings(footprint, drawings, module, de, renderedProps, addProps) {
        drawings.forEach((drawing) => {
            switch (drawing.layers.length) {
                case 0:
                    this.manager.addRenderWarning(`Footprint ${footprint.name} did not have drawing layers`);
                    return;
                case 1:
                    break;
                default:
                    this.manager.addRenderWarning(`Footprint ${footprint.name} had ${drawing.layers.length} drawing layers, expected 1`);
                    return;
            }
            const layer = this.mapFaceLayer(drawing.layers[0], module.layer);
            switch (drawing.drawing.type) {
                case 'segment': {
                    this.renderFootprintSegment(drawing.drawing, module, footprint, layer, de, undefined, addProps);
                    break;
                }
                case 'track': {
                    this.renderFootprintTrack(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                case 'rect': {
                    this.renderFootprintRectangle(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                case 'text': {
                    if (drawing.drawing.ref_designator && drawing.drawing.ref && module.drawProperties[drawing.drawing.ref]) {
                        renderedProps.add(drawing.drawing.ref);
                    }
                    this.renderFootprintText(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                case 'circle': {
                    this.renderFootprintCircle(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                case 'arc': {
                    this.renderFootprintArc(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                case 'curve': {
                    this.renderFootprintCurve(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                case 'polygon': {
                    this.renderFootprintPolygon(drawing.drawing, module, footprint, layer, de, addProps);
                    break;
                }
                default:
                    this.manager.addRenderWarning(`Footprint ${footprint.name} uses unsupported drawing type ${drawing.drawing.type}`);
            }
        });
    }
    colorForFootprintDrawing(layer, drawing, module) {
        // Per-drawing override (baked into the symbol) takes precedence over
        // per-module override (the placement's `bodyColorOverride`, used for
        // "do-not-place" markers and similar per-instance recoloring).
        const drawingOverride = this.colorOverrideToColor(drawing.colorOverride);
        if (drawingOverride) {
            return colorToGraphicsOpts(drawingOverride.color, drawingOverride.alpha);
        }
        const moduleOverride = module ? this.colorOverrideToColor(module.bodyColorOverride) : undefined;
        if (moduleOverride) {
            return colorToGraphicsOpts(moduleOverride.color, moduleOverride.alpha);
        }
        return this.manager.colorProvider.getColorForLayer(layer);
    }
    renderFootprintArc(arc, module, _footprint, layer, de, addProps) {
        const color = this.colorForFootprintDrawing(layer, arc, module);
        const g = this.renderArc(arc, module);
        de.addGraphic(layer, g, color, this.withTypeProp(arc, layer, this.maybeProps(addProps)));
    }
    renderFootprintCurve(curve, module, _footprint, layer, de, addProps) {
        const color = this.colorForFootprintDrawing(layer, curve, module);
        const g = this.renderCurve(curve, module);
        de.addGraphic(layer, g, color, this.withTypeProp(curve, layer, this.maybeProps(addProps)));
    }
    renderFootprintCircle(circle, module, _footprint, layer, de, addProps) {
        const color = this.colorForFootprintDrawing(layer, circle, module);
        const g = this.renderCircle(circle, module);
        de.addGraphic(layer, g, color, this.withTypeProp(circle, layer, this.maybeProps(addProps)));
    }
    renderFootprintPin(pin, module, footprint, de, renderedProps) {
        // Pin segment honors per-instance body recolor when set so a "do-not-place"
        // component's pins match its red body.
        const color = this.colorForFootprintDrawing(pin.layer, pin, module);
        const net = module.pinNets[pin.ref];
        const pinGraphic = this.renderSegment({
            ...pin,
            width: max(pin.width, ConstantsExports.PIN_WIDTH_DEFAULT),
            type: 'segment',
        });
        const center = TransformExports.getXyWithMirror(TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]), module.transform.mirrored);
        let addEl = pinGraphic;
        if (module?.transform.mirrored[0] || module?.transform.mirrored[1] || module?.transform.angle) {
            addEl = new TintedContainer();
            addEl.addChild(pinGraphic);
            addEl.angle = -(module?.transform.angle || 0);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        addEl.position.x += ConstantsExports.WORLD_SCALE * (center[0] + (module?.center[0] || 0));
        addEl.position.y += ConstantsExports.WORLD_SCALE * (center[1] + (module?.center[1] || 0));
        const netProp = this.netProperty({ net }, { [DrawingProperties.Pin]: pinPropertyKey(module.ref, pin.ref) });
        if (footprint && pin.drawings) {
            this.renderFootprintDrawings(footprint, pin.drawings, module, de, renderedProps, netProp?.properties);
        }
        de.addGraphic(pin.layer, addEl, color, this.withTypeProp(pin, pin.layer, netProp));
        if (this.opts.generatePadText) {
            let pinMidpoint = [(pin.start[0] + pin.end[0]) / 2, (pin.start[1] + pin.end[1]) / 2];
            pinMidpoint = TransformExports.rotatePoint(pinMidpoint, -(module?.transform.angle || 0), module?.transform.pos || [0, 0]);
            const pinName = this.resolvePinNameForPadText(pin.name);
            const text = this.renderText({
                type: 'text',
                justify: [0, 0],
                pos: [addEl.position.x / ConstantsExports.WORLD_SCALE + pinMidpoint[0], addEl.position.y / ConstantsExports.WORLD_SCALE + pinMidpoint[1]],
                size: 0.8,
                thickness: 1,
                value: pinName.text,
                textTransforms: pinName.transforms,
            });
            if (text) {
                const padLabelsColor = this.manager.colorProvider.getColorForTool('pad-labels');
                de.addGraphic(pin.layer, text, padLabelsColor, {
                    tags: [{ tag: ConstantsExports.TAG_PAD_LABELS, defaultVisible: false }],
                });
            }
        }
    }
    /**
     * Resolves a pin identity string for generated pad labels.
     *
     * Pin names remain raw identifiers even on stamped models, so this intentionally applies the
     * model-tool dialect independently of the general stamped-text rendering path.
     *
     * @param raw - Raw identity string stored on the pin.
     * @returns Display text and final-string transforms for rendering.
     */
    resolvePinNameForPadText(raw) {
        return distExports$1.ModelText.resolveDisplayText({ raw, dialects: [distExports$1.ModelText.getTextDialectForCadTool(this.model.type)] });
    }
    renderSchematicModule(module, de) {
        const component = this.model.components[module.componentRef];
        if (!component) {
            throw new Error(`Component ${module.componentRef} could not be found!`);
        }
        component.symbols.forEach((footprint) => {
            if (module.footprintRef === footprint.ref) {
                this.renderFootprint(module, footprint, [], de);
            }
        });
    }
    renderFootprintPolygon(poly, module, _footprint, layer, de, addProps) {
        const color = this.colorForFootprintDrawing(layer, poly, module);
        const g = this.renderPolygon(poly, module);
        if (g) {
            de.addGraphic(layer, g, color, this.withTypeProp(poly, layer, this.maybeProps(addProps)));
        }
    }
    renderPolygon(polygon, module) {
        if (polygon.points.length >= this.skipLargePolygonThreshold) {
            if (module) {
                this.manager.addRenderWarning(`[PERF] Skipping rendering of large polygon on module ${module.name} with ${polygon.points.length} vertices`);
            }
            else if (polygon.net) {
                this.manager.addRenderWarning(`[PERF] Skipping rendering of large polygon on net ${polygon.net} with ${polygon.points.length} vertices`);
            }
            else {
                this.manager.addRenderWarning(`[PERF] Skipping rendering of large polygon with ${polygon.points.length} vertices`);
            }
            return undefined;
        }
        const g = new PIXI.Graphics();
        g.clear();
        if (polygon.filled) {
            g.beginFill(ConstantsExports.COLOR_UNFILLED);
        }
        else {
            g.lineStyle({
                color: ConstantsExports.COLOR_UNFILLED,
                width: ConstantsExports.WORLD_SCALE * (polygon.width || 0.1),
            });
        }
        for (let i = 0; i < polygon.points.length; i++) {
            if (i === 0) {
                g.moveTo(ConstantsExports.WORLD_SCALE * polygon.points[i][0], ConstantsExports.WORLD_SCALE * polygon.points[i][1]);
            }
            else {
                g.lineTo(ConstantsExports.WORLD_SCALE * polygon.points[i][0], ConstantsExports.WORLD_SCALE * polygon.points[i][1]);
            }
        }
        if (polygon.filled) {
            g.endFill();
        }
        if (polygon.pos) {
            g.x = polygon.pos[0] * ConstantsExports.WORLD_SCALE;
            g.y = polygon.pos[1] * ConstantsExports.WORLD_SCALE;
        }
        if (polygon.angle) {
            g.angle = -polygon.angle;
        }
        const center = TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]);
        g.angle = -(module?.transform.angle || 0);
        let addEl = g;
        if (module?.transform.mirrored[0] || module?.transform.mirrored[1]) {
            addEl = new TintedContainer();
            addEl.addChild(g);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        addEl.position.x = ConstantsExports.WORLD_SCALE * (center[0] + (module?.center[0] || 0));
        addEl.position.y = ConstantsExports.WORLD_SCALE * (center[1] + (module?.center[1] || 0));
        return addEl;
    }
    renderSchematicReference(ref) {
        let link;
        if (ref.designId) {
            link = {
                designId: ref.designId,
                destinationType: 'design',
            };
        }
        const de = new DrawingElement(this.manager, ref.drawModule, 'modules', ObjectSelectionType.Object, {
            link,
        });
        this.renderSchematicModule(ref.drawModule, de);
        this.manager.addElement(de);
    }
    renderSegment(segment) {
        // Lines are surprisingly hard. It's easy to draw a line. It's not so easy to get the hitbox perfect.
        // Here, we draw a line of the same length on the x axis, and then rotate it about the origin until it
        // has the correct rotation (after adding a good hitbox of course).
        const g = new PIXI.Graphics();
        g.clear();
        const width = ConstantsExports.WORLD_SCALE * max(segment.width, ConstantsExports.SEGMENT_MIN_WIDTH);
        g.lineStyle({
            width: width,
            color: ConstantsExports.COLOR_UNFILLED,
            cap: PIXI.LINE_CAP.ROUND,
        });
        // Compute dimensions and length of line
        const deltaX = ConstantsExports.WORLD_SCALE * (segment.end[0] - segment.start[0]);
        const deltaY = ConstantsExports.WORLD_SCALE * (segment.end[1] - segment.start[1]);
        const lineLength = Math.sqrt(Math.pow(deltaX, 2) + Math.pow(deltaY, 2));
        // Draw the line on X axis
        const linePattern = this.getLineStylePattern(segment.style, width);
        if (linePattern) {
            this.drawLinePattern(g, lineLength, linePattern);
        }
        else {
            g.moveTo(0, 0);
            g.lineTo(lineLength, 0);
        }
        if (!this.disableHitboxes) {
            // Manually calculate a hitbox polygon otherwise PIXI will use the bounding box
            // which is extremely innacurate for traces at an angle. The hitbox has 3 extra
            // points at each end to properly approximate the rounded line caps in the hitbox.
            const halfWidth = (ConstantsExports.WORLD_SCALE * max(segment.width, ConstantsExports.SEGMENT_MIN_HITBOX_WIDTH)) / 2;
            const rootWidth = Math.sqrt(Math.pow(halfWidth, 2) / 2);
            g.hitArea = new PIXI.Polygon([
                { x: 0, y: +halfWidth },
                { x: -rootWidth, y: +rootWidth },
                { x: -halfWidth, y: 0 },
                { x: -rootWidth, y: -rootWidth },
                { x: 0, y: -halfWidth },
                { x: lineLength, y: -halfWidth },
                { x: lineLength + rootWidth, y: -rootWidth },
                { x: lineLength + halfWidth, y: 0 },
                { x: lineLength + rootWidth, y: +rootWidth },
                { x: lineLength, y: +halfWidth },
            ]);
        }
        // Compute the rotation angle for the line
        g.rotation = this.getRotationForLine(deltaX, deltaY);
        // Position the line in the world
        g.position.x = segment.start[0] * ConstantsExports.WORLD_SCALE;
        g.position.y = segment.start[1] * ConstantsExports.WORLD_SCALE;
        return g;
    }
    getLineStylePattern(style, width) {
        if (!style || style === 'solid') {
            return undefined;
        }
        const dash = max(width * 4, 8);
        const gap = max(width * 2, 6);
        const dot = max(width, 2);
        switch (style) {
            case 'dash':
                return [dash, gap];
            case 'dot':
                return [dot, gap];
            case 'dash-dot':
                return [dash, gap, dot, gap];
            default:
                return undefined;
        }
    }
    drawLinePattern(g, lineLength, pattern) {
        let cursor = 0;
        let patternIndex = 0;
        while (cursor < lineLength) {
            const segmentLength = pattern[patternIndex % pattern.length];
            const next = min(cursor + segmentLength, lineLength);
            if (patternIndex % 2 === 0 && next > cursor) {
                g.moveTo(cursor, 0);
                g.lineTo(next, 0);
            }
            cursor = next;
            patternIndex++;
        }
    }
    renderFootprintRectangle(rect, module, _footprint, layer, de, addProps) {
        const color = this.colorForFootprintDrawing(layer, rect, module);
        const g = this.renderRectangle(rect, module);
        de.addGraphic(layer, g, color, this.withTypeProp(rect, layer, this.maybeProps(addProps)));
    }
    renderRectangle(rect, module) {
        const g = new PIXI.Graphics();
        if (rect.filled) {
            g.beginFill(ConstantsExports.COLOR_UNFILLED);
        }
        if (rect.strokeWidth) {
            g.lineStyle({
                color: ConstantsExports.COLOR_UNFILLED,
                width: constrainPositive(rect.strokeWidth) * ConstantsExports.WORLD_SCALE,
            });
        }
        const width = rect.end[0] - rect.start[0];
        const height = rect.end[1] - rect.start[1];
        const drawRect = new PIXI.Rectangle(ConstantsExports.WORLD_SCALE * (width > 0 ? rect.start[0] : rect.end[0]), ConstantsExports.WORLD_SCALE * (height > 0 ? rect.start[1] : rect.end[1]), ConstantsExports.WORLD_SCALE * Math.abs(width), ConstantsExports.WORLD_SCALE * Math.abs(height));
        if (rect.lineStyle === 'dash' || rect.lineStyle === 'dot' || rect.lineStyle === 'dash-dot') {
            drawDashedRect(g, drawRect.x, drawRect.y, drawRect.width, drawRect.height, rect.lineStyle);
        }
        else {
            g.drawRect(drawRect.x, drawRect.y, drawRect.width, drawRect.height);
        }
        if (rect.filled) {
            g.endFill();
        }
        // TODO: I'm not sure this rectangle rotation code is correct
        const center = TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]);
        g.hitArea = drawRect;
        g.angle = -(module?.transform.angle || 0);
        // Apply mirroring to a container element, not the graphic itself because scaling are based on
        // the local axes of the graphics element and not the global axis, but we want to mirror
        // on global axes
        let addEl = g;
        if (module?.transform.mirrored[0] || module?.transform.mirrored[1]) {
            addEl = new TintedContainer();
            addEl.addChild(g);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        addEl.position.x = ConstantsExports.WORLD_SCALE * (center[0] + (module?.center[0] || 0));
        addEl.position.y = ConstantsExports.WORLD_SCALE * (center[1] + (module?.center[1] || 0));
        return addEl;
    }
    colorOverrideToColor(colorOverride, defaultColor, defaultAlpha = 1) {
        if (!colorOverride || colorOverride.length !== 4) {
            if (defaultColor === undefined) {
                return undefined;
            }
            return { color: defaultColor, alpha: defaultAlpha };
        }
        return {
            color: PIXI.utils.rgb2hex([colorOverride[0] / 255, colorOverride[1] / 255, colorOverride[2] / 255]),
            alpha: colorOverride[3],
        };
    }
    renderFootprintSegment(segment, module, _footprint, layer, de, net, addProps) {
        const color = this.colorForFootprintDrawing(layer, segment, module);
        // Lines are surprisingly hard. It's easy to draw a line. It's not so easy to get the hitbox perfect.
        // Here, we draw a line of the same length on the x axis, and then rotate it about the origin until it
        // has the correct rotation (after adding a good hitbox of course).
        const g = new PIXI.Graphics();
        g.clear();
        const width = ConstantsExports.WORLD_SCALE * max(segment.width, ConstantsExports.SEGMENT_MIN_WIDTH);
        g.lineStyle({
            width,
            color: ConstantsExports.COLOR_UNFILLED,
            cap: PIXI.LINE_CAP.ROUND,
        });
        // Compute dimensions and length of line
        const flip = [this.design.grid?.invertX ? -1 : 1, this.design.grid?.invertY ? -1 : 1];
        const scaled_start = [flip[0] * segment.start[0] * ConstantsExports.WORLD_SCALE, flip[1] * segment.start[1] * ConstantsExports.WORLD_SCALE];
        const deltaX = flip[0] * ConstantsExports.WORLD_SCALE * TransformExports.roundToZero(segment.end[0] - segment.start[0], 2);
        const deltaY = flip[1] * ConstantsExports.WORLD_SCALE * TransformExports.roundToZero(segment.end[1] - segment.start[1], 2);
        const lineLength = Math.sqrt(Math.pow(deltaX, 2) + Math.pow(deltaY, 2));
        // Compute the rotation angle for the line
        const rotation = this.getRotationForLine(deltaX, deltaY);
        // Draw the line on X axis
        const p2 = TransformExports.rotatePoint([lineLength, 0], (rotation * 180) / Math.PI, [0, 0]);
        g.moveTo(scaled_start[0], scaled_start[1]);
        g.lineTo(p2[0] + scaled_start[0], p2[1] + scaled_start[1]);
        if (!this.disableHitboxes) {
            // Manually calculate a hitbox polygon otherwise PIXI will use the bounding box
            // which is extremely innacurate for traces at an angle. The hitbox has 3 extra
            // points at each end to properly approximate the rounded line caps in the hitbox.
            const halfWidth = (ConstantsExports.WORLD_SCALE * max(segment.width, ConstantsExports.SEGMENT_MIN_HITBOX_WIDTH)) / 2;
            const rootWidth = Math.sqrt(Math.pow(halfWidth, 2) / 2);
            g.hitArea = new PIXI.Polygon([
                [0, +halfWidth],
                [-rootWidth, +rootWidth],
                [-halfWidth, 0],
                [-rootWidth, -rootWidth],
                [0, -halfWidth],
                [lineLength, -halfWidth],
                [lineLength + rootWidth, -rootWidth],
                [lineLength + halfWidth, 0],
                [lineLength + rootWidth, +rootWidth],
                [lineLength, +halfWidth],
            ].map((point) => {
                const rotated = TransformExports.rotatePoint(point, (rotation * 180) / Math.PI, [0, 0]);
                return {
                    x: rotated[0] + scaled_start[0],
                    y: rotated[1] + scaled_start[1],
                };
            }));
        }
        const center = TransformExports.rotatePoint([0, 0], -module.transform.angle, module.transform.pos);
        g.angle = -module.transform.angle;
        // Apply mirroring to a container element, not the graphic itself because scaling are based on
        // the local axes of the graphics element and not the global axis, but we want to mirror
        // on global axes
        let addEl = g;
        if (module.transform.mirrored[0] || module.transform.mirrored[1]) {
            addEl = new TintedContainer();
            addEl.addChild(g);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        addEl.position.x = ConstantsExports.WORLD_SCALE * (center[0] + module.center[0]);
        addEl.position.y = ConstantsExports.WORLD_SCALE * (center[1] + module.center[1]);
        de.addGraphic(layer, addEl, color, this.withTypeProp(segment, layer, this.netProperty({ net }, addProps)));
    }
    renderFootprintTrack(track, module, footprint, layer, de, addProps) {
        return this.renderFootprintSegment(track, module, footprint, layer, de, track.net, addProps);
    }
    getRotationForLine(deltaX, deltaY) {
        let rotation;
        if (deltaX === 0) {
            if (deltaY > 0) {
                rotation = Math.PI / 2;
            }
            else {
                rotation = -Math.PI / 2;
            }
        }
        else if (deltaY === 0) {
            if (deltaX > 0) {
                rotation = 0;
            }
            else {
                rotation = Math.PI;
            }
        }
        else if (deltaY < 0) {
            if (deltaX < 0) {
                rotation = Math.PI + Math.atan(deltaY / deltaX);
            }
            else {
                rotation = Math.atan(deltaY / deltaX);
            }
        }
        else {
            if (deltaX > 0) {
                rotation = Math.atan(deltaY / deltaX);
            }
            else {
                rotation = Math.PI + Math.atan(deltaY / deltaX);
            }
        }
        return rotation;
    }
    renderFootprintText(text, module, _footprint, layer, de, addProps) {
        let isTextPositionAbsolute = false;
        let mirror = text.mirror || [false, false];
        if (text.ref_designator && text.ref) {
            if (module.drawProperties[text.ref]) {
                if (module.drawProperties[text.ref].hidden) {
                    return;
                }
                mirror = combineMirrors(module.drawProperties[text.ref].mirror, text.mirror);
                isTextPositionAbsolute = !module.drawProperties[text.ref].isPositionOffset;
                text = {
                    ...text,
                    ...module.drawProperties[text.ref],
                    value: module.drawProperties[text.ref].value,
                    mirror: mirror,
                };
            }
            else {
                // Don't render refs without a value
                return;
            }
        }
        // Honor `colorOverride` after the drawProperties spread above —
        // per-instance label tints (e.g. Allegro red "do-not-place"
        // markers) ride along on the resolved Text.
        const color = this.colorForFootprintDrawing(layer, text, module);
        const adjustedPinName = !shouldCompensateOrcadPinName(this.model) ? undefined : (_footprint?.pins
            ?.map((pin) => getLegacyPinNameForRendering(this.model, text, pin.start))
            .find((candidate) => candidate.pos[0] !== text.pos[0] || candidate.pos[1] !== text.pos[1]));
        if (adjustedPinName) {
            text = adjustedPinName;
        }
        if (!isTextPositionAbsolute) {
            text = {
                ...text,
                pos: TransformExports.translatePoint(TransformExports.getXyWithMirror(TransformExports.rotatePoint(text.pos, -module.transform.angle), module.transform.mirrored), module.center),
                angle: (text.angle || 0) + (module.transform.angle || 0),
            };
            if (text.rejustifyOnRotate) {
                const { justify, rot } = TransformExports.rejustifyTextUpwards(text.justify, {
                    rot: text.angle || 0,
                    mirrored: mirror,
                });
                text.justify = justify;
                text.angle = rot.rot;
                text.mirror = rot.mirrored;
            }
        }
        const g = this.renderText(text);
        if (g) {
            const props = this.netProperty(text, addProps);
            let settings = this.withTypeProp(text, layer, props);
            if (text.contrastWithLayer) {
                settings = { ...(settings || {}), contrastWithLayer: true };
            }
            de.addGraphic(layer, g, color, settings);
            this.addTextBoundsToDrawingEelment(de, layer, g, color, props?.properties);
        }
    }
    addTextBoundsToDrawingEelment(de, layer, g, color, addProps) {
        if (this.opts.generateTextBounds) {
            // Render text bounds
            const box = new PIXI.Graphics();
            box.beginFill(ConstantsExports.COLOR_UNFILLED);
            box.drawCircle(g.x, g.y, ConstantsExports.WORLD_SCALE * ConstantsExports.SEGMENT_MIN_WIDTH);
            box.endFill();
            box.lineStyle({
                color: ConstantsExports.COLOR_UNFILLED,
                width: (ConstantsExports.WORLD_SCALE * ConstantsExports.SEGMENT_MIN_WIDTH) / 10,
            });
            const bounds = g.getBounds();
            box.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);
            de.addGraphic(layer, box, color, {
                tags: [{ tag: ConstantsExports.TAG_TEXT_BOUNDS, defaultVisible: false }],
                properties: addProps,
            });
        }
    }
    renderTextBox(textbox, defaultLayer) {
        const text = this.renderText(textbox.text);
        if (!text) {
            return [];
        }
        const textBounds = text.getBounds();
        const rectGraphic = this.renderRectangle({
            ...textbox.background,
            start: TransformExports.subtractPoints(TransformExports.scalePoint([textBounds.left, textBounds.top], 1 / ConstantsExports.WORLD_SCALE), textbox.text.padding || [0, 0]),
            end: TransformExports.translatePoint(TransformExports.scalePoint([textBounds.right, textBounds.bottom], 1 / ConstantsExports.WORLD_SCALE), textbox.text.padding || [0, 0]),
        }, undefined);
        return [
            { g: text, layer: defaultLayer, contrastWithLayer: textbox.text.contrastWithLayer },
            { g: rectGraphic, layer: textbox.backgroundLayer },
        ];
    }
    renderText(text) {
        if (!text.value || this.opts.skipRenderText) {
            return;
        }
        const resolvedText = resolveTextForRendering(this.model, text);
        text = resolvedText.text;
        let font = this.design?.font?.default;
        if (text.font) {
            font = text.font;
        }
        // TODO - ratio
        // TODO (jafin) - Adjusting text with baseline causes clipping on the top, we should just adjust
        //  the position instead.
        // TODO (jafin) - alignment only works for multiline. Should offset from the calculated width.
        const barTransforms = resolvedText.barTransforms;
        if (text.value.length === 0) {
            return;
        }
        let pixiText;
        const fontDef = font ? this.model.fonts?.fonts[font] : undefined;
        if (fontDef && fontDef.type === 'project') {
            // Use Hershey rendering for project fonts (vector/stroke fonts)
            pixiText = this.renderHersheyText(text, fontDef, barTransforms);
        }
        else {
            // Use raster rendering for browser fonts or when no font is found
            if (font && !fontDef) {
                this.manager.addRenderWarning(`Font ${font} was not found in project`);
            }
            pixiText = this.renderRasterText(text, barTransforms, fontDef?.type === 'browser' ? fontDef : undefined);
        }
        if (!pixiText) {
            return undefined;
        }
        let addEl = pixiText;
        if (text.mirror) {
            addEl = new TintedContainer();
            addEl.addChild(pixiText);
            if (text.mirror && text.mirror[0]) {
                addEl.scale.y *= -1;
            }
            if (text.mirror && text.mirror[1]) {
                addEl.scale.x *= -1;
            }
        }
        const padding = this.getTextPadding(text);
        addEl.position.x += ConstantsExports.WORLD_SCALE * text.pos[0] + padding[0];
        addEl.position.y += ConstantsExports.WORLD_SCALE * text.pos[1] + padding[1];
        if (text.bounds && text.clipToBounds) {
            const maskGraphics = new PIXI.Graphics()
                .beginFill(ConstantsExports.COLOR_UNFILLED)
                .drawRect(addEl.position.x, addEl.position.y, text.bounds[0] * ConstantsExports.WORLD_SCALE, text.bounds[1] * ConstantsExports.WORLD_SCALE)
                .endFill();
            const container = new TintedContainer();
            container.addChild(maskGraphics);
            container.addChild(addEl);
            container.mask = maskGraphics;
            return container;
        }
        return addEl;
    }
    renderHersheyText(text, font, barTransforms = []) {
        const pixiTextGraphic = new PIXI.Graphics();
        const space = ' '.charCodeAt(0);
        const R = 'R'.charCodeAt(0);
        let xOffset = 0;
        const baseXoffset = 0;
        const lineHeight = 1;
        let yOffset = 0;
        let lineNo = 0;
        let onNewline = true;
        const textScale = max(text.size, ConstantsExports.MIN_TEXT_SIZE);
        let wordWrapWidth;
        if (text.bounds) {
            if (text.wordWrap) {
                wordWrapWidth = getTextWordWrapWidth(text);
            }
        }
        const baseThickness = max(text.thickness, ConstantsExports.MIN_TEXT_THICKNESS) / 10;
        const fontRenderScale = font.fontRenderScale * ConstantsExports.WORLD_SCALE;
        pixiTextGraphic.lineStyle({
            color: ConstantsExports.COLOR_UNFILLED,
            cap: PIXI.LINE_CAP.ROUND,
            join: PIXI.LINE_JOIN.ROUND,
            width: baseThickness * ConstantsExports.WORLD_SCALE,
        });
        let maxX = 0, maxY = 0;
        const charPositions = [];
        const glyphs = distExports$1.Parse.stringToFontStrokes(font, text.value);
        for (let glyphNo = 0; glyphNo < text.value.length; ++glyphNo) {
            const char = text.value[glyphNo];
            if (char === '\r') {
                charPositions.push({
                    position: [xOffset, yOffset],
                    endPosition: [xOffset, yOffset],
                    onNewline,
                    lineNo,
                });
                continue;
            }
            if (char === '\n') {
                charPositions.push({
                    position: [xOffset, yOffset],
                    endPosition: [xOffset, yOffset],
                    onNewline,
                    lineNo,
                });
                xOffset = baseXoffset;
                yOffset += lineHeight * font.interlineRatio * ConstantsExports.WORLD_SCALE;
                onNewline = true;
                ++lineNo;
                continue;
            }
            const glyph = glyphs[glyphNo];
            let glyphStartX = 0.0;
            let glyphEndX = 0.0;
            let glyphWidth = 0.0;
            let penDown = false;
            if (glyph === font.unknownChar) {
                this.manager.addRenderWarning(`Unknown character ${char} in font ${font.name}`);
            }
            for (let i = 0; i < glyph.length; i += 2) {
                const coordinate = [glyph.charCodeAt(i), glyph.charCodeAt(i + 1)];
                if (i < 2) {
                    glyphStartX = (coordinate[0] + font.fontRenderOffset[0]) * fontRenderScale;
                    glyphEndX = (coordinate[1] + font.fontRenderOffset[0]) * fontRenderScale;
                    glyphWidth = glyphEndX - glyphStartX;
                    // Handle word wrapping
                    if (wordWrapWidth && (glyphEndX + xOffset) * textScale >= wordWrapWidth) {
                        xOffset = baseXoffset;
                        yOffset += lineHeight * font.interlineRatio * ConstantsExports.WORLD_SCALE;
                        onNewline = true;
                        ++lineNo;
                    }
                    charPositions.push({
                        position: [-glyphStartX / 2 + xOffset, yOffset],
                        endPosition: [-glyphEndX / 2 + xOffset, yOffset],
                        onNewline,
                        lineNo,
                    });
                }
                else if (coordinate[0] === space && coordinate[1] === R) {
                    penDown = false;
                }
                else {
                    const x = xOffset + (coordinate[0] + font.fontRenderOffset[0]) * fontRenderScale - glyphStartX;
                    const y = yOffset + (coordinate[1] + font.fontRenderOffset[1]) * fontRenderScale;
                    if (penDown) {
                        pixiTextGraphic.lineTo(x, y);
                        maxX = max(x, maxX);
                        maxY = max(y, maxY);
                    }
                    else {
                        pixiTextGraphic.moveTo(x, y);
                        penDown = true;
                    }
                    charPositions[charPositions.length - 1].position[0] = min(charPositions[charPositions.length - 1].position[0], x);
                    charPositions[charPositions.length - 1].endPosition[0] = max(charPositions[charPositions.length - 1].endPosition[0], x);
                }
            }
            xOffset += glyphWidth + font.charSpacing * ConstantsExports.WORLD_SCALE;
        }
        const justify = [text.justify[0] / 2 + 0.5, text.justify[1] / 2 + 0.5];
        const texturizedGraphic = this.texturizeGraphic(pixiTextGraphic);
        texturizedGraphic.x -= justify[0] * maxX * textScale;
        texturizedGraphic.y -= justify[1] * maxY * textScale;
        texturizedGraphic.scale.set(textScale, textScale);
        if (text.attr?.includes('italic')) {
            // TODO: Instead of skewing the whole object, skew just the characters
            // for proper multiline and positioning support
            texturizedGraphic.skew.set(-font.italicTilt, pixiTextGraphic.skew.y);
        }
        const addEl = new TintedContainer();
        addEl.addChild(texturizedGraphic);
        addEl.angle = -(text.angle || 0);
        if (barTransforms.length > 0) {
            for (const transform of barTransforms) {
                if (transform.from < 0 || transform.to > text.value.length || transform.from >= transform.to) {
                    this.manager.addRenderWarning('Bar transform range exceeded character length');
                    continue;
                }
                const offset = charPositions[transform.from];
                let end = charPositions[transform.to];
                if (!offset) {
                    this.manager.addRenderWarning('Bar transform offset exceeded character length');
                    continue;
                }
                if (!end) {
                    if (transform.to === charPositions.length) {
                        end = charPositions[charPositions.length - 1];
                    }
                    else {
                        this.manager.addRenderWarning('Bar transform end exceeded character length');
                        continue;
                    }
                }
                const transformG = this.renderSegment({
                    type: 'segment',
                    start: TransformExports.scalePoint(TransformExports.rotatePoint(TransformExports.translatePoint(offset.position, [0, -font.overbarSpacing * textScale * ConstantsExports.WORLD_SCALE]), pixiTextGraphic.angle), [textScale / ConstantsExports.WORLD_SCALE, textScale / ConstantsExports.WORLD_SCALE]),
                    end: TransformExports.scalePoint(TransformExports.rotatePoint(TransformExports.translatePoint(end.endPosition, [0, -font.overbarSpacing * textScale * ConstantsExports.WORLD_SCALE]), pixiTextGraphic.angle), [textScale / ConstantsExports.WORLD_SCALE, textScale / ConstantsExports.WORLD_SCALE]),
                    width: (transform.width ?? baseThickness) * textScale,
                });
                transformG.x -= justify[0] * maxX * textScale;
                transformG.y -= justify[1] * maxY * textScale;
                addEl.addChild(transformG);
            }
        }
        return addEl;
    }
    getTextPadding(text) {
        return getTextPadding(text);
    }
    renderRasterText(text, barTransforms, browserFont) {
        let textScale = 0.1 * ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
        const textAttrs = [...(browserFont?.attrs ?? []), ...(text.attr ?? [])];
        const style = {
            fontFamily: browserFont?.name || text.font || 'monospace',
            fill: 0xffffff,
            align: text.textAlign,
            fontStyle: textAttrs.find((t) => t === 'italic') ? 'italic' : 'normal',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            fontWeight: textAttrs.find((t) => t.startsWith('bold')) || 'normal',
        };
        let fontSize = this.getRasterTextFontSize(style, text.size, textScale, browserFont?.rasterSizeCorrection);
        style.fontSize = fontSize;
        if (text.bounds && text.wordWrap) {
            const wordWrapWidth = getRasterTextWordWrapWidth(text, textScale);
            if (wordWrapWidth !== undefined) {
                style.wordWrapWidth = wordWrapWidth;
                style.wordWrap = true;
                style.breakWords = true;
            }
        }
        let textStyle = new PIXI.TextStyle(style);
        let size = PIXI.TextMetrics.measureText(text.value, textStyle);
        // Browsers limit how big a canvas can be. If the text would exceed
        // that limit, rasterize it at a smaller font size instead — same
        // size on screen, just blurrier. Repeat a few times because word
        // wrap makes the measurement shrink in steps, not smoothly.
        for (let attempt = 0; attempt < 3; attempt++) {
            const clampFactor = getRasterTextCanvasClampFactor(size.width, size.height, globalThis.devicePixelRatio ?? 1);
            if (clampFactor >= 1) {
                break;
            }
            textScale *= clampFactor;
            fontSize *= clampFactor;
            style.fontSize = fontSize;
            if (style.wordWrap && typeof style.wordWrapWidth === 'number') {
                style.wordWrapWidth *= clampFactor;
            }
            textStyle = new PIXI.TextStyle(style);
            size = PIXI.TextMetrics.measureText(text.value, textStyle);
        }
        if (getRasterTextCanvasClampFactor(size.width, size.height, globalThis.devicePixelRatio ?? 1) < 1) {
            this.manager.addRenderWarning(`Text element could not be rasterized within canvas size limits, skipping`);
            return undefined;
        }
        const pixiText = new PIXI.Text(text.value, textStyle);
        pixiText.anchor.set(text.justify[0] / 2 + 0.5, text.justify[1] / 2 + 0.5);
        pixiText.angle = -(text.angle || 0);
        pixiText.scale.set(1 / textScale, 1 / textScale);
        let addEl = pixiText;
        if (barTransforms.length > 0) {
            addEl = new TintedContainer();
            addEl.addChild(pixiText);
            for (const transform of barTransforms) {
                if (transform.from < 0 || transform.to > text.value.length || transform.from >= transform.to) {
                    this.manager.addRenderWarning('Bar transform range exceeded character length');
                    continue;
                }
                const fullSize = PIXI.TextMetrics.measureText(text.value, textStyle);
                const offset = PIXI.TextMetrics.measureText(text.value.substring(0, transform.from), textStyle);
                const barSize = PIXI.TextMetrics.measureText(text.value.substring(transform.from, transform.to), textStyle);
                const yVal = (pixiText.anchor.y * -offset.height) / textScale / ConstantsExports.WORLD_SCALE;
                const transformG = this.renderSegment({
                    type: 'segment',
                    start: TransformExports.rotatePoint([(offset.width - fullSize.width * pixiText.anchor.x) / textScale / ConstantsExports.WORLD_SCALE, yVal], pixiText.angle),
                    end: TransformExports.rotatePoint([(offset.width + barSize.width - fullSize.width * pixiText.anchor.x) / textScale / ConstantsExports.WORLD_SCALE, yVal], pixiText.angle),
                    width: transform.width ?? text.size / 10,
                });
                addEl.addChild(transformG);
            }
        }
        return addEl;
    }
    getRasterTextFontSize(style, textSize, textScale, rasterSizeCorrection) {
        const metricStyle = new PIXI.TextStyle({
            ...style,
            fontSize: RASTER_TEXT_METRIC_FONT_SIZE,
            wordWrap: false,
        });
        const glyphHeight = this.measureRasterGlyphHeight('M', metricStyle) ||
            PIXI.TextMetrics.measureText('M', metricStyle, false).fontProperties.ascent ||
            RASTER_TEXT_METRIC_FONT_SIZE;
        return getRasterTextFontSizeForGlyphHeight(textSize, textScale, glyphHeight, rasterSizeCorrection);
    }
    measureRasterGlyphHeight(text, style) {
        const canvas = PIXI.settings.ADAPTER.createCanvas();
        const context = canvas.getContext('2d');
        if (!context) {
            return undefined;
        }
        context.font = style.toFontString();
        const metrics = context.measureText(text);
        const height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
        return height > 0 ? height : undefined;
    }
    renderJunction(junction) {
        return this.renderCircle({
            type: 'circle',
            center: junction.position,
            filled: true,
            radius: junction.radiusOverride ?? ConstantsExports.JUNCTION_RADIUS_DEFAULT,
        });
    }
    renderCircle(circle, module) {
        const g = new PIXI.Graphics();
        const scaleFactor = ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
        if (circle.filled) {
            g.beginFill(ConstantsExports.COLOR_UNFILLED);
        }
        else {
            g.lineStyle({
                color: ConstantsExports.COLOR_UNFILLED,
                width: scaleFactor * max(circle.width, ConstantsExports.MIN_CIRCLE_RADIUS),
                cap: PIXI.LINE_CAP.ROUND,
            });
        }
        g.drawCircle(scaleFactor * circle.center[0], scaleFactor * circle.center[1], scaleFactor * max(circle.radius, ConstantsExports.MIN_CIRCLE_RADIUS));
        if (circle.filled) {
            g.endFill();
        }
        // Apply mirroring to a container element, not the graphic itself because scaling are based on
        // the local axes of the graphics element and not the global axis, but we want to mirror
        // on global axes
        const center = TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]);
        g.angle = -(module?.transform.angle || 0);
        g.scale.set(ConstantsExports.WORLD_SCALE / scaleFactor);
        let addEl = g;
        if (module?.transform.mirrored[0] || module?.transform.mirrored[1]) {
            addEl = new TintedContainer();
            addEl.addChild(g);
            if (module.transform.mirrored[0]) {
                addEl.scale.y *= -1;
            }
            if (module.transform.mirrored[1]) {
                addEl.scale.x *= -1;
            }
        }
        addEl.position.x = ConstantsExports.WORLD_SCALE * (center[0] + (module?.center[0] || 0));
        addEl.position.y = ConstantsExports.WORLD_SCALE * (center[1] + (module?.center[1] || 0));
        return addEl;
    }
    renderPad(module, _footprint, pad, de, throughHoleLayers) {
        let scaleFactor = ConstantsExports.WORLD_SCALE;
        let graphicScale = 1;
        if (pad.shape === 'circle' || pad.shape === 'oval' || pad.shape === 'roundrect') {
            scaleFactor = ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
            graphicScale = ConstantsExports.WORLD_SCALE / ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
        }
        const diameter = [
            scaleFactor * max(pad.diameter[0], ConstantsExports.SEGMENT_MIN_WIDTH),
            scaleFactor * max(pad.diameter[1], ConstantsExports.SEGMENT_MIN_WIDTH),
        ];
        let hitArea;
        for (let layer of pad.layers || throughHoleLayers) {
            layer = this.mapFaceLayer(layer, module.layer);
            const color = this.manager.colorProvider.getColorForLayer(layer);
            const g = new PIXI.Graphics();
            g.name = pad.name;
            g.scale.set(graphicScale, graphicScale);
            if (pad.shape === 'circle' || pad.shape === 'oval') {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.drawEllipse(0, 0, diameter[0] / 2, diameter[1] / 2);
                g.endFill();
                hitArea = new PIXI.Ellipse(0, 0, diameter[0] / 2, diameter[1] / 2);
            }
            else if (pad.shape === 'rect' ||
                (pad.shape === 'roundrect' && (!pad.radius || pad.radius < ConstantsExports.MIN_ROUND_RECT_RADIUS))) {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.drawRect(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1]);
                g.endFill();
                hitArea = new PIXI.Rectangle(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1]);
            }
            else if (pad.shape === 'roundrect') {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.drawRoundedRect(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1], ConstantsExports.WORLD_SCALE * max(pad.radius, ConstantsExports.MIN_ROUND_RECT_RADIUS));
                g.endFill();
                hitArea = new PIXI.Rectangle(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1]);
            }
            else if (pad.shape === 'custom') {
                if (!pad.polygons || pad.polygons.length === 0) {
                    this.manager.addRenderWarning(`Polygons prop was expected for '${pad.shape}'.`);
                    continue;
                }
                pad.polygons.forEach((path) => {
                    g.beginFill(ConstantsExports.COLOR_UNFILLED);
                    g.moveTo(ConstantsExports.WORLD_SCALE * path[0][0], ConstantsExports.WORLD_SCALE * path[0][1]);
                    for (let i = 1; i < path.length; i++) {
                        g.lineTo(ConstantsExports.WORLD_SCALE * path[i][0], ConstantsExports.WORLD_SCALE * path[i][1]);
                    }
                    g.endFill();
                    hitArea = new PIXI.Polygon(path.map(([x, y]) => new PIXI.Point(ConstantsExports.WORLD_SCALE * x, ConstantsExports.WORLD_SCALE * y)));
                });
                if (pad.polygons.length > 1) {
                    this.manager.addRenderWarning(`Pads with multiple polygons not supported correctly.`);
                }
            }
            else {
                // todo
                this.manager.addRenderWarning(`Drawing of pad shape '${pad.shape}' has not been implemented.`);
                continue;
            }
            if (pad.drillDiameter) {
                g.beginHole();
                g.drawEllipse(0, 0, (scaleFactor * max(pad.drillDiameter[0], 0)) / 2, (scaleFactor * max(pad.drillDiameter[1], 0)) / 2);
                g.endHole();
            }
            const rotated = TransformExports.getXyWithMirror(TransformExports.rotatePoint(pad.pos, -module.transform.angle, module.transform.pos), module.transform.mirrored);
            const translate = [rotated[0] + module.center[0], rotated[1] + module.center[1]];
            const angle = TransformExports.clampRotation(-pad.angle - module.transform.angle);
            g.angle = angle;
            g.position.x = ConstantsExports.WORLD_SCALE * translate[0];
            g.position.y = ConstantsExports.WORLD_SCALE * translate[1];
            if (!this.disableHitboxes) {
                g.hitArea = hitArea || g.getBounds();
            }
            de.addGraphic(layer, g, color, this.withTypeProp(pad, layer, this.netProperty({ net: module.pinNets[pad.ref] }, { [DrawingProperties.Pad]: pinPropertyKey(module.ref, pad.ref) })));
            // ## Text calculations
            // Start with an initial font and get the measurements.
            // Scale the font to fit inside the area (minus 10% padding).
            // If area height > width, then rotate it 90. Otherwise renders at 0.
            if (this.opts.generatePadText) {
                const net = module.pinNets[pad.ref]?.split('/').at(-1);
                const resolvedNet = net ? this.resolvePinNameForPadText(net) : undefined;
                const netText = resolvedNet?.text ?? '';
                let textValue = '';
                let netOffset = 0;
                if (Boolean(pad.name) && Boolean(netText)) {
                    textValue = `${pad.name}\n${netText}`;
                    netOffset = pad.name.length + 1;
                }
                else if (pad.name) {
                    textValue = pad.name;
                }
                else {
                    textValue = netText;
                }
                if (!textValue) {
                    continue;
                }
                const diameters = [...pad.diameter];
                const bWidth = Math.max(...diameters);
                const bHeight = Math.min(...diameters.map((v) => Math.abs(v)).filter((d) => d > 0));
                let rotateText = pad.diameter[0] < pad.diameter[1];
                if (bWidth > bHeight && ((angle > 45 && angle < 135) || (angle > 225 && angle < 315))) {
                    rotateText = !rotateText;
                }
                const text = this.renderText({
                    type: 'text',
                    justify: [0, 0],
                    pos: translate,
                    size: Math.min(bWidth / textValue.length, bHeight),
                    thickness: 1,
                    value: textValue,
                    textTransforms: resolvedNet?.transforms.map((transform) => transform.type === 'bar' ?
                        { ...transform, from: transform.from + netOffset, to: transform.to + netOffset }
                        : transform),
                    angle: rotateText ? -90 : 0,
                });
                if (text) {
                    const color = this.manager.colorProvider.getColorForTool('pad-labels');
                    de.addGraphic(layer, text, color, {
                        tags: [{ tag: ConstantsExports.TAG_PAD_LABELS, defaultVisible: false }],
                    });
                }
            }
        }
    }
    renderOverlayAnnotations(overlayBounds, annotations, layer, tags) {
        return annotations
            .map((annotation) => {
            switch (annotation.type) {
                case 'image':
                    return this.renderOverlayImageAnnotation(overlayBounds, annotation, layer, tags);
                default:
                    throw new Error(`Unknown annotation type: ${annotation.type}`);
            }
        })
            .reduce((p, c) => p.concat(c), []);
    }
    renderOverlayImageAnnotation(overlayBounds, annotation, layer, tags) {
        const imageSprite = PIXI.Sprite.from(annotation.imageUri);
        imageSprite.height = annotation.size.height * ConstantsExports.WORLD_SCALE;
        imageSprite.width = annotation.size.width * ConstantsExports.WORLD_SCALE;
        switch (annotation.location) {
            case 'top-left':
            default:
                imageSprite.x = overlayBounds.x + (annotation.offset?.x || 0) * ConstantsExports.WORLD_SCALE;
                imageSprite.y = overlayBounds.y + (annotation.offset?.y || 0) * ConstantsExports.WORLD_SCALE;
                break;
        }
        switch (annotation.anchor) {
            case 'left-center':
                imageSprite.anchor.set(0, 0.5);
                break;
            case 'left-bottom':
                imageSprite.anchor.set(0, 0);
                break;
            case 'left-top':
            default:
                imageSprite.anchor.set(0, 1);
                break;
        }
        const graphics = [];
        if (annotation.mask) {
            if (annotation.mask.type === 'zone') {
                throw new Error('Zone elements not supported for mask');
            }
            // TODO - multiple elements could be returned by called to renderDrawingElement here
            // If we actually end up using image annotations should improve the handlinghere.
            const maskGraphics = this.renderDrawingElement(annotation.mask, tags, layer);
            if (!maskGraphics.length) {
                throw new Error('Mask did not result in a rendered graphic');
            }
            const maskGraphic = maskGraphics[0].g;
            maskGraphic.x = imageSprite.x - imageSprite.anchor.x * imageSprite.width;
            maskGraphic.y = imageSprite.y - imageSprite.anchor.y * imageSprite.height;
            imageSprite.mask = maskGraphic;
            graphics.push({
                graphic: maskGraphic,
                isMask: true,
            });
            const [maskBgs] = this.renderDrawingElement(annotation.mask, tags, layer);
            const maskBg = maskBgs.g;
            maskBg.x = imageSprite.x - imageSprite.anchor.x * imageSprite.width;
            maskBg.y = imageSprite.y - imageSprite.anchor.y * imageSprite.height;
            graphics.push({
                graphic: maskBg,
            });
        }
        graphics.push({
            graphic: imageSprite,
        });
        if (annotation.border) {
            if (annotation.border.type === 'zone') {
                throw new Error('Zone elements not supported for border');
            }
            const borderGraphics = this.renderDrawingElement(annotation.border, tags, layer);
            if (!borderGraphics.length) {
                throw new Error('Mask did not result in a rendered graphic');
            }
            const borderGraphic = borderGraphics[0].g;
            borderGraphic.x = imageSprite.x - imageSprite.anchor.x * imageSprite.width;
            borderGraphic.y = imageSprite.y - imageSprite.anchor.y * imageSprite.height;
            graphics.push({
                graphic: borderGraphic,
            });
        }
        return graphics;
    }
    renderLabel(label) {
        if (!label.value || label.value.length === 0 || this.opts.skipRenderText) {
            return;
        }
        // const size = PIXI.TextMetrics.measureText(label.value, textStyle);
        const addEl = new TintedContainer();
        const xPad = ConstantsExports.WORLD_SCALE * 1;
        const yPad = ConstantsExports.WORLD_SCALE * 0.4;
        const poly = new PIXI.Graphics().lineStyle({
            color: ConstantsExports.COLOR_UNFILLED,
            width: ConstantsExports.WORLD_SCALE * ConstantsExports.SEGMENT_MIN_WIDTH,
        });
        const text = {
            type: 'text',
            justify: [-1, 0],
            pos: [0, 0],
            size: 1.27,
            thickness: 1,
            value: label.value,
        };
        switch (label.orientation) {
            case 'R':
                poly.angle = 0;
                text.justify = [1, 0];
                text.pos[0] += -xPad / 2;
                text.pos[1] += 0;
                break;
            case 'L':
                poly.angle = 180;
                text.justify = [-1, 0];
                text.pos[0] += xPad / 2;
                text.pos[1] += 0;
                break;
            case 'D':
                poly.angle = 90;
                text.justify = [-1, 0];
                text.pos[0] += 0;
                text.pos[1] += -xPad / 2;
                text.angle = -270;
                break;
            case 'U':
                poly.angle = 270;
                text.justify = [1, 0];
                text.pos[0] += 0;
                text.pos[1] += xPad / 2;
                text.angle = -270;
                break;
        }
        const pixiText = this.renderText(text);
        if (!pixiText) {
            return undefined;
        }
        const size = pixiText.getBounds();
        switch (label.orientation) {
            case 'D':
            case 'U': {
                const width = size.width;
                size.width = size.height;
                size.height = width;
                break;
            }
        }
        if (label.filled) {
            poly.beginFill(ConstantsExports.COLOR_UNFILLED);
        }
        switch (label.shape) {
            case 'neutral':
                // Rectangle
                poly.drawPolygon(new PIXI.Polygon([
                    // Go up
                    { x: 0 - xPad, y: 0 + yPad + size.height / 2 },
                    // Back
                    { x: 0 - xPad * 1.5 - size.width, y: 0 + yPad + size.height / 2 },
                    // Down
                    { x: 0 - xPad * 1.5 - size.width, y: 0 - yPad - size.height / 2 },
                    // Forward
                    { x: 0 - xPad, y: 0 - yPad - size.height / 2 },
                    // Back up
                    { x: 0 - xPad, y: 0 + yPad + size.height / 2 },
                ]));
                break;
            case 'bidirectional':
                // Arrows pointing left and right
                poly.drawPolygon(new PIXI.Polygon([
                    // Start at the point
                    { x: 0, y: 0 },
                    // Go up
                    { x: 0 - xPad, y: 0 + yPad + size.height / 2 },
                    // Back
                    { x: 0 - xPad - size.width, y: 0 + yPad + size.height / 2 },
                    // Other Point
                    { x: 0 - xPad * 2 - size.width, y: 0 },
                    // Down
                    { x: 0 - xPad - size.width, y: 0 - yPad - size.height / 2 },
                    // Forward
                    { x: 0 - xPad, y: 0 - yPad - size.height / 2 },
                    // Back to point
                    { x: 0, y: 0 },
                ]));
                break;
            case 'output':
                poly.x = 1.5 * xPad + size.width;
                poly.scale.x *= -1;
                pixiText.x -= xPad / 2;
            // intentional fall through to 'input' case
            default:
            case 'input':
                // Arrow pointing right
                poly.drawPolygon(new PIXI.Polygon([
                    // Start at the point
                    { x: 0, y: 0 },
                    // Go up
                    { x: 0 - xPad, y: 0 + yPad + size.height / 2 },
                    // Back
                    { x: 0 - xPad * 1.5 - size.width, y: 0 + yPad + size.height / 2 },
                    // Down
                    { x: 0 - xPad * 1.5 - size.width, y: 0 - yPad - size.height / 2 },
                    // Forward
                    { x: 0 - xPad, y: 0 - yPad - size.height / 2 },
                    // Back to point
                    { x: 0, y: 0 },
                ]));
                break;
        }
        if (label.filled) {
            poly.endFill();
        }
        addEl.addChild(poly);
        addEl.addChild(pixiText);
        addEl.position.x = ConstantsExports.WORLD_SCALE * label.pos[0];
        addEl.position.y = ConstantsExports.WORLD_SCALE * label.pos[1];
        return addEl;
    }
    netProperty(netInfo = {}, addProps) {
        const propNets = resolveNetPropertyValues(netInfo, this.opts.multiInstanceId);
        if (propNets.length === 0) {
            return this.maybeProps(addProps);
        }
        return {
            properties: {
                ...addProps,
                [DrawingProperties.Net]: propNets,
            },
        };
    }
    withTypeProp(obj, layer, addProps) {
        if (!this.generateOpts) {
            return addProps;
        }
        return {
            ...(addProps || {}),
            object: {
                type: obj.type,
                layer,
                obj,
            },
        };
    }
    maybeProps(addProps) {
        if (addProps) {
            return {
                properties: addProps,
            };
        }
        return undefined;
    }
    renderImage(image) {
        const container = new TintedContainer();
        const w = image.width * ConstantsExports.WORLD_SCALE;
        const h = image.height * ConstantsExports.WORLD_SCALE;
        container.x = image.pos[0] * ConstantsExports.WORLD_SCALE;
        container.y = image.pos[1] * ConstantsExports.WORLD_SCALE;
        if (image.angle) {
            container.angle = -image.angle;
        }
        // Skeleton placeholder
        const skeleton = new PIXI.Graphics();
        skeleton.beginFill(0xe0e0e0, 0.5);
        skeleton.drawRoundedRect(0, 0, w, h, 4);
        skeleton.endFill();
        container.addChild(skeleton);
        // Resolve URL (may be async for auth-fetched blob URLs) and acquire texture
        const revokeBlobUrl = (url) => {
            if (url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        };
        const loadTexture = (url) => {
            this.manager.textureManager.acquire(url, (texture) => {
                if (container.destroyed) {
                    revokeBlobUrl(url);
                    return;
                }
                const sprite = new PIXI.Sprite(texture);
                sprite.width = w;
                sprite.height = h;
                container.removeChild(skeleton);
                skeleton.destroy();
                container.addChild(sprite);
                revokeBlobUrl(url);
            }, (_error) => {
                revokeBlobUrl(url);
            });
        };
        const resolved = this.opts.imageUrlResolver?.(image.imageRef) ?? image.imageRef;
        if (typeof resolved === 'string') {
            loadTexture(resolved);
        }
        else {
            resolved.then(loadTexture).catch(() => {
                // Keep skeleton visible on resolve error
            });
        }
        return container;
    }
    /** Renders a `RichText` SchDrawing as a flow of `PIXI.Text` nodes
     *  inside a container positioned at the drawing's bbox. Each
     *  paragraph wraps at the bbox width; runs within a paragraph
     *  inherit body-level defaults (font-family / size / weight / style)
     *  and override per-run (bold / italic / sizePt / font / color).
     *  Sizes flow from points → mm → pixi world units (`PT_TO_MM` ×
     *  `WORLD_SCALE`); the renderer leaves Pixi to rasterize text
     *  natively at viewport DPI. */
    renderRichText(richText) {
        const container = new TintedContainer();
        container.x = richText.pos[0] * ConstantsExports.WORLD_SCALE;
        container.y = richText.pos[1] * ConstantsExports.WORLD_SCALE;
        const widthWorld = richText.width * ConstantsExports.WORLD_SCALE;
        const defaultPt = richText.defaultSizePt ?? 8;
        const defaultBold = richText.defaultBold === true;
        const defaultItalic = richText.defaultItalic === true;
        const defaultFont = richText.defaultFont ?? 'Arial, sans-serif';
        const ptToWorld = (pt) => ((pt * 25.4) / 72) * ConstantsExports.WORLD_SCALE * 2;
        // Rasterize text at TEXT_OVERSAMPLE × the on-screen pt size and
        // scale the resulting Text node down by 1/TEXT_OVERSAMPLE so the
        // displayed size matches the authored pt while staying crisp under
        // modest zoom. Mirrors `renderRasterText`'s textScale pattern.
        const TEXT_OVERSAMPLE = 10;
        let cursorY = 0;
        for (const paragraph of richText.paragraphs) {
            const lineHeight = paragraphLineHeight(paragraph, defaultPt);
            const lineHeightWorld = ptToWorld(lineHeight) * 1.15;
            let cursorX = 0;
            for (const run of paragraph.runs) {
                const sizePt = run.sizePt ?? defaultPt;
                const fontSizeWorld = ptToWorld(sizePt);
                const oversampledFontSize = fontSizeWorld * TEXT_OVERSAMPLE;
                const isBold = run.bold ?? defaultBold;
                const isItalic = run.italic ?? defaultItalic;
                const fontFamily = run.font ?? defaultFont;
                // Per-run override wins over body-level default. When neither
                // is set we fall back to COLOR_UNFILLED so the layer tint
                // (propagated by TintedContainer) defines the on-screen color
                // — same default-tinting story as other primitives. When an
                // explicit color is set, we still leave the Text node inside
                // the TintedContainer so layer tint modulates the authored
                // color rather than overriding it.
                const explicitColor = this.colorOverrideToColor(run.color ?? richText.defaultColor);
                for (const token of tokenizeRun(run.value)) {
                    if (token.length === 0) {
                        continue;
                    }
                    // Whitespace tokens at the start of any line — whether
                    // the start of paragraph or freshly wrapped — would
                    // produce a leading-space artifact. Drop them before
                    // constructing the Text node to avoid wasted work and a
                    // visible indent at the line head.
                    if (cursorX === 0 && /^\s+$/.test(token)) {
                        continue;
                    }
                    const text = new PIXI.Text(token, {
                        fontFamily,
                        fontSize: oversampledFontSize,
                        fontWeight: isBold ? 'bold' : 'normal',
                        fontStyle: isItalic ? 'italic' : 'normal',
                        fill: explicitColor?.color ?? ConstantsExports.COLOR_UNFILLED,
                    });
                    if (explicitColor !== undefined) {
                        text.alpha = explicitColor.alpha;
                    }
                    text.scale.set(1 / TEXT_OVERSAMPLE);
                    const displayWidth = text.width;
                    if (cursorX + displayWidth > widthWorld && cursorX > 0) {
                        cursorY += lineHeightWorld;
                        cursorX = 0;
                        if (/^\s+$/.test(token)) {
                            text.destroy();
                            continue;
                        }
                    }
                    text.x = cursorX;
                    text.y = cursorY;
                    container.addChild(text);
                    cursorX += displayWidth;
                }
            }
            cursorY += lineHeightWorld;
        }
        return container;
    }
}

/**
 * Global constant CCW defines counterclockwise direction of arc
 * @type {boolean}
 */
const CCW = true;

/**
 * Global constant CW defines clockwise direction of arc
 * @type {boolean}
 */
const CW = false;

/**
 * Defines orientation for face of the polygon: clockwise, counterclockwise
 * or not orientable in the case of self-intersection
 * @type {{CW: number, CCW: number, NOT_ORIENTABLE: number}}
 */
const ORIENTATION = {CCW:-1, CW:1, NOT_ORIENTABLE: 0};

const PIx2 = 2 * Math.PI;

const INSIDE$2 = 1;
const OUTSIDE$1 = 0;
const BOUNDARY$1 = 2;
const CONTAINS = 3;
const INTERLACE = 4;

const OVERLAP_SAME$1 = 1;
const OVERLAP_OPPOSITE$1 = 2;

const NOT_VERTEX$1 = 0;
const START_VERTEX$1 = 1;
const END_VERTEX$1 = 2;

var Constants = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BOUNDARY: BOUNDARY$1,
    CCW: CCW,
    CONTAINS: CONTAINS,
    CW: CW,
    END_VERTEX: END_VERTEX$1,
    INSIDE: INSIDE$2,
    INTERLACE: INTERLACE,
    NOT_VERTEX: NOT_VERTEX$1,
    ORIENTATION: ORIENTATION,
    OUTSIDE: OUTSIDE$1,
    OVERLAP_OPPOSITE: OVERLAP_OPPOSITE$1,
    OVERLAP_SAME: OVERLAP_SAME$1,
    PIx2: PIx2,
    START_VERTEX: START_VERTEX$1
});

/**
 * Created by Alex Bol on 2/18/2017.
 */

/**
 * Floating point comparison tolerance.
 * Default value is 0.000001 (10e-6)
 * @type {number}
 */
let DP_TOL = 0.000001;

/**
 * Set new floating point comparison tolerance
 * @param {number} tolerance
 */
function setTolerance(tolerance) {DP_TOL = tolerance;}

/**
 * Get floating point comparison tolerance
 * @returns {number}
 */
function getTolerance() {return DP_TOL;}

const DECIMALS = 3;

/**
 * Returns *true* if value comparable to zero
 * @param {number} x
 * @param {number} y
 * @return {boolean}
 */
function EQ_0(x) {
    return (x < DP_TOL && x > -DP_TOL);
}

/**
 * Returns *true* if two values are equal up to DP_TOL
 * @param {number} x
 * @param {number} y
 * @return {boolean}
 */
function EQ(x, y) {
    return (x - y < DP_TOL && x - y > -DP_TOL);
}

/**
 * Returns *true* if first argument greater than second argument up to DP_TOL
 * @param {number} x
 * @param {number} y
 * @return {boolean}
 */
function GT(x, y) {
    return (x - y > DP_TOL);
}

/**
 * Returns *true* if first argument greater than or equal to second argument up to DP_TOL
 * @param {number} x
 * @param {number} y
 * @returns {boolean}
 */
function GE(x, y) {
    return (x - y > -DP_TOL);
}

/**
 * Returns *true* if first argument less than second argument up to DP_TOL
 * @param {number} x
 * @param {number} y
 * @return {boolean}
 */
function LT(x, y) {
    return (x - y < -DP_TOL)
}

/**
 * Returns *true* if first argument less than or equal to second argument up to DP_TOL
 * @param {number} x
 * @param {number} y
 * @return {boolean}
 */
function LE(x, y) {
    return (x - y < DP_TOL);
}

var Utils$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DECIMALS: DECIMALS,
    EQ: EQ,
    EQ_0: EQ_0,
    GE: GE,
    GT: GT,
    LE: LE,
    LT: LT,
    getTolerance: getTolerance,
    setTolerance: setTolerance
});

let Flatten = {
    Utils: Utils$1,
    Errors: undefined,
    Matrix: undefined,
    Planar_set: undefined,
    Point: undefined,
    Vector: undefined,
    Line: undefined,
    Circle: undefined,
    Segment: undefined,
    Arc: undefined,
    Box: undefined,
    Edge: undefined,
    Face: undefined,
    Ray: undefined,
    Ray_shooting: undefined,
    Multiline: undefined,
    Polygon: undefined,
    Distance: undefined,
    Inversion: undefined
};

for (let c in Constants) {Flatten[c] = Constants[c];}

Object.defineProperty(Flatten, 'DP_TOL', {
    get:function(){return getTolerance()}, 
    set:function(value){setTolerance(value);}
});

/**
 * Created by Alex Bol on 2/19/2017.
 */


/**
 * Class of system errors
 */
class Errors {
    /**
     * Throw error ILLEGAL_PARAMETERS when cannot instantiate from given parameter
     * @returns {ReferenceError}
     */
    static get ILLEGAL_PARAMETERS() {
        return new ReferenceError('Illegal Parameters');
    }

    /**
     * Throw error ZERO_DIVISION to catch situation of zero division
     * @returns {Error}
     */
    static get ZERO_DIVISION() {
        return new Error('Zero division');
    }

    /**
     * Error to throw from BooleanOperations module in case when fixBoundaryConflicts not capable to fix it
     * @returns {Error}
     */
    static get UNRESOLVED_BOUNDARY_CONFLICT() {
        return new Error('Unresolved boundary conflict in boolean operation');
    }

    /**
     * Error to throw from LinkedList:testInfiniteLoop static method
     * in case when circular loop detected in linked list
     * @returns {Error}
     */
    static get INFINITE_LOOP() {
        return new Error('Infinite loop');
    }

    static get CANNOT_COMPLETE_BOOLEAN_OPERATION() {
        return new Error('Cannot complete boolean operation')
    }

    static get CANNOT_INVOKE_ABSTRACT_METHOD() {
        return new Error('Abstract method cannot be invoked');
    }

    static get OPERATION_IS_NOT_SUPPORTED() {
        return new Error('Operation is not supported')
    }

    static get UNSUPPORTED_SHAPE_TYPE() {
        return new Error('Unsupported shape type')
    }
}

Flatten.Errors = Errors;

/**
 * Class implements bidirectional non-circular linked list. <br/>
 * LinkedListElement - object of any type that has properties next and prev.
 */
class LinkedList {
    constructor(first, last) {
        this.first = first;
        this.last = last || this.first;
    }

    [Symbol.iterator]() {
        let value = undefined;
        return {
            next: () => {
                value = value ? value.next : this.first;
                return {value: value, done: value === undefined};
            }
        };
    };

    /**
     * Return number of elements in the list
     * @returns {number}
     */
    get size() {
        let counter = 0;
        for (let edge of this) {
            counter++;
        }
        return counter;
    }

    /**
     * Return array of elements from start to end,
     * If start or end not defined, take first as start, last as end
     * @returns {Array}
     */
    toArray(start=undefined, end=undefined) {
        let elements = [];
        let from = start || this.first;
        let to = end || this.last;
        let element = from;
        if (element === undefined) return elements;
        do {
            elements.push(element);
            element = element.next;
        } while (element !== to.next);
        return elements;
    }


    /**
     * Append new element to the end of the list
     * @param {LinkedListElement} element
     * @returns {LinkedList}
     */
    append(element) {
        if (this.isEmpty()) {
            this.first = element;
        } else {
            element.prev = this.last;
            this.last.next = element;
        }

        // update edge to be last
        this.last = element;

        // nullify non-circular links
        this.last.next = undefined;
        this.first.prev = undefined;
        return this;
    }

    /**
     * Insert new element to the list after elementBefore
     * @param {LinkedListElement} newElement
     * @param {LinkedListElement} elementBefore
     * @returns {LinkedList}
     */
    insert(newElement, elementBefore) {
        if (this.isEmpty()) {
            this.first = newElement;
            this.last = newElement;
        }
        else if (elementBefore === null || elementBefore === undefined) {
            newElement.next = this.first;
            this.first.prev = newElement;
            this.first = newElement;
        }
        else {
            /* set links to new element */
            let elementAfter = elementBefore.next;
            elementBefore.next = newElement;
            if (elementAfter) elementAfter.prev = newElement;

            /* set links from new element */
            newElement.prev = elementBefore;
            newElement.next = elementAfter;

            /* extend list if new element added after the last element */
            if (this.last === elementBefore)
                this.last = newElement;
        }
        // nullify non-circular links
        this.last.next = undefined;
        this.first.prev = undefined;
        return this;
    }

    /**
     * Remove element from the list
     * @param {LinkedListElement} element
     * @returns {LinkedList}
     */
    remove(element) {
        // special case if last edge removed
        if (element === this.first && element === this.last) {
            this.first = undefined;
            this.last = undefined;
        } else {
            // update linked list
            if (element.prev) element.prev.next = element.next;
            if (element.next) element.next.prev = element.prev;
            // update first if need
            if (element === this.first) {
                this.first = element.next;
            }
            // update last if need
            if (element === this.last) {
                this.last = element.prev;
            }
        }
        return this;
    }

    /**
     * Return true if list is empty
     * @returns {boolean}
     */
    isEmpty() {
        return this.first === undefined;
    }

    /**
     * Throw an error if circular loop detected in the linked list
     * @param {LinkedListElement} first element to start iteration
     * @throws {Errors.INFINITE_LOOP}
     */
    static testInfiniteLoop(first) {
        let edge = first;
        let controlEdge = first;
        do {
            if (edge != first && edge === controlEdge) {
                throw Errors.INFINITE_LOOP;  // new Error("Infinite loop")
            }
            edge = edge.next;
            controlEdge = controlEdge.next.next;
        } while (edge != first)
    }
}

const defaultAttributes = {
    stroke: "black"
};

class SVGAttributes {
    constructor(args = defaultAttributes) {
        for(const property in args) {
            this[property] = args[property];
        }
        this.stroke = args.stroke ?? defaultAttributes.stroke;
    }

    toAttributesString() {
        return Object.keys(this)
            .reduce( (acc, key) =>
                    acc + (this[key] !== undefined ? this.toAttrString(key, this[key]) : "")
            , ``)
    }

    toAttrString(key, value) {
        const SVGKey = key === "className" ? "class" : this.convertCamelToKebabCase(key);
        return value === null ? `${SVGKey} ` : `${SVGKey}="${value.toString()}" `
    }

    convertCamelToKebabCase(str) {
        return str
            .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            .join('-')
            .toLowerCase();
    }
}

function convertToString(attrs) {
    return new SVGAttributes(attrs).toAttributesString()
}

/**
 * Intersection
 *
 * */


function intersectLine2Line(line1, line2) {
    let ip = [];

    let [A1, B1, C1] = line1.standard;
    let [A2, B2, C2] = line2.standard;

    /* Cramer's rule */
    let det = A1 * B2 - B1 * A2;
    let detX = C1 * B2 - B1 * C2;
    let detY = A1 * C2 - C1 * A2;

    if (!Flatten.Utils.EQ_0(det)) {
        let x, y;

        if (B1 === 0) {        // vertical line x  = C1/A1, where A1 == +1 or -1
            x = C1/A1;
            y = detY / det;
        }
        else if (B2 === 0) {   // vertical line x = C2/A2, where A2 = +1 or -1
            x = C2/A2;
            y = detY / det;
        }
        else if (A1 === 0) {   // horizontal line y = C1/B1, where B1 = +1 or -1
            x = detX / det;
            y = C1/B1;
        }
        else if (A2 === 0) {   // horizontal line y = C2/B2, where B2 = +1 or -1
            x = detX / det;
            y = C2/B2;
        }
        else {
            x = detX / det;
            y = detY / det;
        }

        ip.push(new Flatten.Point(x, y));
    }

    return ip;
}

function intersectLine2Circle(line, circle) {
    let ip = [];
    let prj = circle.pc.projectionOn(line);            // projection of circle center on a line
    let dist = circle.pc.distanceTo(prj)[0];           // distance from circle center to projection

    if (Flatten.Utils.EQ(dist, circle.r)) {            // line tangent to circle - return single intersection point
        ip.push(prj);
    } else if (Flatten.Utils.LT(dist, circle.r)) {       // return two intersection points
        let delta = Math.sqrt(circle.r * circle.r - dist * dist);
        let v_trans, pt;

        v_trans = line.norm.rotate90CCW().multiply(delta);
        pt = prj.translate(v_trans);
        ip.push(pt);

        v_trans = line.norm.rotate90CW().multiply(delta);
        pt = prj.translate(v_trans);
        ip.push(pt);
    }
    return ip;
}

function intersectLine2Box(line, box) {
    let ips = [];
    for (let seg of box.toSegments()) {
        let ips_tmp = intersectSegment2Line(seg, line);
        for (let pt of ips_tmp) {
            if (!ptInIntPoints(pt, ips)) {
                ips.push(pt);
            }
        }
    }
    return ips;
}

function intersectLine2Arc(line, arc) {
    let ip = [];

    if (intersectLine2Box(line, arc.box).length === 0) {
        return ip;
    }

    let circle = new Flatten.Circle(arc.pc, arc.r);
    let ip_tmp = intersectLine2Circle(line, circle);
    for (let pt of ip_tmp) {
        if (pt.on(arc)) {
            ip.push(pt);
        }
    }

    return ip;
}

function intersectSegment2Line(seg, line) {
    let ip = [];

    // Boundary cases
    if (seg.ps.on(line)) {
        ip.push(seg.ps);
    }
    // If both ends lay on line, return two intersection points
    if (seg.pe.on(line) && !seg.isZeroLength()) {
        ip.push(seg.pe);
    }

    if (ip.length > 0) {
        return ip;          // done, intersection found
    }

    // If zero-length segment and nothing found, return no intersections
    if (seg.isZeroLength()) {
        return ip;
    }

    // Not a boundary case, check if both points are on the same side and
    // hence there is no intersection
    if (seg.ps.leftTo(line) && seg.pe.leftTo(line) ||
        !seg.ps.leftTo(line) && !seg.pe.leftTo(line)) {
        return ip;
    }

    // Calculate intersection between lines
    let line1 = new Flatten.Line(seg.ps, seg.pe);
    return intersectLine2Line(line1, line);
}

function intersectSegment2Segment(seg1, seg2) {
    let ip = [];

    // Special case of seg1 zero length
    if (seg1.isZeroLength()) {
        if (seg1.ps.on(seg2)) {
            ip.push(seg1.ps);
        }
        return ip;
    }

    // Special case of seg2 zero length
    if (seg2.isZeroLength()) {
        if (seg2.ps.on(seg1)) {
            ip.push(seg2.ps);
        }
        return ip;
    }

    // Neither seg1 nor seg2 is zero length
    let line1 = new Flatten.Line(seg1.ps, seg1.pe);
    let line2 = new Flatten.Line(seg2.ps, seg2.pe);

    // Check overlapping between segments in case of incidence
    // If segments touching, add one point. If overlapping, add two points
    if (line1.incidentTo(line2)) {
        if (seg1.ps.on(seg2)) {
            ip.push(seg1.ps);
        }
        if (seg1.pe.on(seg2)) {
            ip.push(seg1.pe);
        }
        if (seg2.ps.on(seg1) && !seg2.ps.equalTo(seg1.ps) && !seg2.ps.equalTo(seg1.pe)) {
            ip.push(seg2.ps);
        }
        if (seg2.pe.on(seg1) && !seg2.pe.equalTo(seg1.ps) && !seg2.pe.equalTo(seg1.pe)) {
            ip.push(seg2.pe);
        }
    } else {                /* not incident - parallel or intersect */
        // Calculate intersection between lines
        let new_ip = intersectLine2Line(line1, line2);
        if (new_ip.length > 0) {
            if (isPointInSegmentBox(new_ip[0], seg1) && isPointInSegmentBox(new_ip[0], seg2)) {
                ip.push(new_ip[0]);
            }
        }
    }
    return ip;
}

function isPointInSegmentBox(point, segment) {
    const box = segment.box;
    return Flatten.Utils.LE(point.x, box.xmax) && Flatten.Utils.GE(point.x, box.xmin) &&
        Flatten.Utils.LE(point.y, box.ymax) && Flatten.Utils.GE(point.y, box.ymin)
}

function intersectSegment2Circle(segment, circle) {
    let ips = [];

    // Special case of zero length segment
    if (segment.isZeroLength()) {
        let [dist, _] = segment.ps.distanceTo(circle.pc);
        if (Flatten.Utils.EQ(dist, circle.r)) {
            ips.push(segment.ps);
        }
        return ips;
    }

    // Non zero-length segment
    let line = new Flatten.Line(segment.ps, segment.pe);

    let ips_tmp = intersectLine2Circle(line, circle);

    for (let ip of ips_tmp) {
        if (ip.on(segment)) {
            ips.push(ip);
        }
    }

    return ips;
}

function intersectSegment2Arc(segment, arc) {
    let ip = [];

    // Special case of zero-length segment
    if (segment.isZeroLength()) {
        if (segment.ps.on(arc)) {
            ip.push(segment.ps);
        }
        return ip;
    }

    // Non-zero length segment
    let line = new Flatten.Line(segment.ps, segment.pe);
    let circle = new Flatten.Circle(arc.pc, arc.r);

    let ip_tmp = intersectLine2Circle(line, circle);

    for (let pt of ip_tmp) {
        if (pt.on(segment) && pt.on(arc)) {
            ip.push(pt);
        }
    }
    return ip;

}

function intersectSegment2Box(segment, box) {
    let ips = [];
    for (let seg of box.toSegments()) {
        let ips_tmp = intersectSegment2Segment(seg, segment);
        for (let ip of ips_tmp) {
            ips.push(ip);
        }
    }
    return ips;
}

function intersectCircle2Circle(circle1, circle2) {
    let ip = [];

    let vec = new Flatten.Vector(circle1.pc, circle2.pc);

    let r1 = circle1.r;
    let r2 = circle2.r;

    // Degenerated circle
    if (Flatten.Utils.EQ_0(r1) || Flatten.Utils.EQ_0(r2))
        return ip;

    // In case of equal circles return one leftmost point
    if (Flatten.Utils.EQ_0(vec.x) && Flatten.Utils.EQ_0(vec.y) && Flatten.Utils.EQ(r1, r2)) {
        ip.push(circle1.pc.translate(-r1, 0));
        return ip;
    }

    let dist = circle1.pc.distanceTo(circle2.pc)[0];

    if (Flatten.Utils.GT(dist, r1 + r2))               // circles too far, no intersections
        return ip;

    if (Flatten.Utils.LT(dist, Math.abs(r1 - r2)))     // one circle is contained within another, no intersections
        return ip;

    // Normalize vector.
    vec.x /= dist;
    vec.y /= dist;

    let pt;

    // Case of touching from outside or from inside - single intersection point
    // TODO: check this specifically not sure if correct
    if (Flatten.Utils.EQ(dist, r1 + r2) || Flatten.Utils.EQ(dist, Math.abs(r1 - r2))) {
        pt = circle1.pc.translate(r1 * vec.x, r1 * vec.y);
        ip.push(pt);
        return ip;
    }

    // Case of two intersection points

    // Distance from first center to center of common chord:
    //   a = (r1^2 - r2^2 + d^2) / 2d
    // Separate for better accuracy
    let a = (r1 * r1) / (2 * dist) - (r2 * r2) / (2 * dist) + dist / 2;

    let mid_pt = circle1.pc.translate(a * vec.x, a * vec.y);
    let h = Math.sqrt(r1 * r1 - a * a);
    // let norm;

    // norm = vec.rotate90CCW().multiply(h);
    pt = mid_pt.translate(vec.rotate90CCW().multiply(h));
    ip.push(pt);

    // norm = vec.rotate90CW();
    pt = mid_pt.translate(vec.rotate90CW().multiply(h));
    ip.push(pt);

    return ip;
}

function intersectCircle2Box(circle, box) {
    let ips = [];
    for (let seg of box.toSegments()) {
        let ips_tmp = intersectSegment2Circle(seg, circle);
        for (let ip of ips_tmp) {
            ips.push(ip);
        }
    }
    return ips;
}

function intersectArc2Arc(arc1, arc2) {
    let ip = [];

    // Special case: overlapping arcs
    // May return up to 4 intersection points
    if (arc1.pc.equalTo(arc2.pc) && Flatten.Utils.EQ(arc1.r, arc2.r)) {
        let pt;

        pt = arc1.start;
        if (pt.on(arc2))
            ip.push(pt);

        pt = arc1.end;
        if (pt.on(arc2))
            ip.push(pt);

        pt = arc2.start;
        if (pt.on(arc1)) ip.push(pt);

        pt = arc2.end;
        if (pt.on(arc1)) ip.push(pt);

        return ip;
    }

    // Common case
    let circle1 = new Flatten.Circle(arc1.pc, arc1.r);
    let circle2 = new Flatten.Circle(arc2.pc, arc2.r);
    let ip_tmp = circle1.intersect(circle2);
    for (let pt of ip_tmp) {
        if (pt.on(arc1) && pt.on(arc2)) {
            ip.push(pt);
        }
    }
    return ip;
}

function intersectArc2Circle(arc, circle) {
    let ip = [];

    // Case when arc center incident to circle center
    // Return arc's end points as 2 intersection points
    if (circle.pc.equalTo(arc.pc) && Flatten.Utils.EQ(circle.r, arc.r)) {
        ip.push(arc.start);
        ip.push(arc.end);
        return ip;
    }

    // Common case
    let circle1 = circle;
    let circle2 = new Flatten.Circle(arc.pc, arc.r);
    let ip_tmp = intersectCircle2Circle(circle1, circle2);
    for (let pt of ip_tmp) {
        if (pt.on(arc)) {
            ip.push(pt);
        }
    }
    return ip;
}

function intersectArc2Box(arc, box) {
    let ips = [];
    for (let seg of box.toSegments()) {
        let ips_tmp = intersectSegment2Arc(seg, arc);
        for (let ip of ips_tmp) {
            ips.push(ip);
        }
    }
    return ips;
}

function intersectEdge2Segment(edge, segment) {
    return edge.isSegment ? intersectSegment2Segment(edge.shape, segment) : intersectSegment2Arc(segment, edge.shape);
}

function intersectEdge2Arc(edge, arc) {
    return edge.isSegment ? intersectSegment2Arc(edge.shape, arc) : intersectArc2Arc(edge.shape, arc);
}

function intersectEdge2Line(edge, line) {
    return edge.isSegment ? intersectSegment2Line(edge.shape, line) : intersectLine2Arc(line, edge.shape);
}

function intersectEdge2Ray(edge, ray) {
    return edge.isSegment ? intersectRay2Segment(ray, edge.shape) : intersectRay2Arc(ray, edge.shape);
}

function intersectEdge2Circle(edge, circle) {
    return edge.isSegment ? intersectSegment2Circle(edge.shape, circle) : intersectArc2Circle(edge.shape, circle);
}

function intersectSegment2Polygon(segment, polygon) {
    let ip = [];

    for (let edge of polygon.edges) {
        for (let pt of intersectEdge2Segment(edge, segment)) {
            ip.push(pt);
        }
    }

    return ip;
}

function intersectArc2Polygon(arc, polygon) {
    let ip = [];

    for (let edge of polygon.edges) {
        for (let pt of intersectEdge2Arc(edge, arc)) {
            ip.push(pt);
        }
    }

    return ip;
}

function intersectLine2Polygon(line, polygon) {
    let ip = [];

    if (polygon.isEmpty()) {
        return ip;
    }

    for (let edge of polygon.edges) {
        for (let pt of intersectEdge2Line(edge, line)) {
            if (!ptInIntPoints(pt, ip)) {
                ip.push(pt);
            }
        }
    }

    return line.sortPoints(ip);
}

function intersectCircle2Polygon(circle, polygon) {
    let ip = [];

    if (polygon.isEmpty()) {
        return ip;
    }

    for (let edge of polygon.edges) {
        for (let pt of intersectEdge2Circle(edge, circle)) {
            ip.push(pt);
        }
    }

    return ip;
}

function intersectEdge2Edge(edge1, edge2) {
    if (edge1.isSegment) {
        return intersectEdge2Segment(edge2, edge1.shape)
    }
    else if (edge1.isArc) {
        return intersectEdge2Arc(edge2, edge1.shape)
    }
    else if (edge1.isLine) {
        return intersectEdge2Line(edge2, edge1.shape)
    }
    else if (edge1.isRay) {
        return intersectEdge2Ray(edge2, edge1.shape)
    }
    return []
}

function intersectEdge2Polygon(edge, polygon) {
    let ip = [];

    if (polygon.isEmpty() || edge.shape.box.not_intersect(polygon.box)) {
        return ip;
    }

    let resp_edges = polygon.edges.search(edge.shape.box);

    for (let resp_edge of resp_edges) {
        ip = [...ip, ...intersectEdge2Edge(edge, resp_edge)];
    }

    return ip;
}

function intersectMultiline2Polygon(multiline, polygon) {
    let ip = [];

    if (polygon.isEmpty() || multiline.size === 0) {
        return ip;
    }

    for (let edge of multiline) {
        ip = [...ip, ...intersectEdge2Polygon(edge, polygon)];
    }

    return ip;
}

function intersectPolygon2Polygon(polygon1, polygon2) {
    let ip = [];

    if (polygon1.isEmpty() || polygon2.isEmpty()) {
        return ip;
    }

    if (polygon1.box.not_intersect(polygon2.box)) {
        return ip;
    }

    for (let edge1 of polygon1.edges) {
        ip = [...ip, ...intersectEdge2Polygon(edge1, polygon2)];
    }

    return ip;
}

function intersectShape2Polygon(shape, polygon) {
    if (shape instanceof Flatten.Line) {
        return intersectLine2Polygon(shape, polygon);
    }
    else if (shape instanceof Flatten.Segment) {
        return intersectSegment2Polygon(shape, polygon);
    }
    else if (shape instanceof Flatten.Arc) {
        return intersectArc2Polygon(shape, polygon);
    }
    else {
        return [];
    }
}

function ptInIntPoints(new_pt, ip) {
    return ip.some( pt => pt.equalTo(new_pt) )
}

function createLineFromRay(ray) {
    return new Flatten.Line(ray.start, ray.norm)
}
function intersectRay2Segment(ray, segment) {
    return intersectSegment2Line(segment, createLineFromRay(ray))
        .filter(pt => ray.contains(pt));
}

function intersectRay2Arc(ray, arc) {
    return intersectLine2Arc(createLineFromRay(ray), arc)
        .filter(pt => ray.contains(pt))
}

function intersectRay2Circle(ray, circle) {
    return intersectLine2Circle(createLineFromRay(ray), circle)
        .filter(pt => ray.contains(pt))
}

function intersectRay2Box(ray, box) {
    return intersectLine2Box(createLineFromRay(ray), box)
        .filter(pt => ray.contains(pt))
}

function intersectRay2Line(ray, line) {
    return intersectLine2Line(createLineFromRay(ray), line)
        .filter(pt => ray.contains(pt))
}

function intersectRay2Ray(ray1, ray2) {
    return intersectLine2Line(createLineFromRay(ray1), createLineFromRay(ray2))
        .filter(pt => ray1.contains(pt))
        .filter(pt => ray2.contains(pt))
}

function intersectRay2Polygon(ray, polygon) {
    return intersectLine2Polygon(createLineFromRay(ray), polygon)
        .filter(pt => ray.contains(pt))
}

function intersectShape2Shape(shape1, shape2) {
    if (shape1.intersect && shape1.intersect instanceof Function) {
        return shape1.intersect(shape2)
    }
    throw Errors.UNSUPPORTED_SHAPE_TYPE
}

function intersectShape2Multiline(shape, multiline) {
    let ip = [];
    for (let edge of multiline) {
        ip = [...ip, ...intersectShape2Shape(shape, edge.shape)];
    }
    return ip;
}

function intersectMultiline2Multiline(multiline1, multiline2) {
    let ip = [];
    for (let edge1 of multiline1) {
        for (let edge2 of multiline2) {
            ip = [...ip, ...intersectShape2Shape(edge1.shape, edge2.shape)];
        }
    }
    return ip;
}

/**
 * Class Multiline represent connected path of [edges]{@link Flatten.Edge}, where each edge may be
 * [segment]{@link Flatten.Segment}, [arc]{@link Flatten.Arc}, [line]{@link Flatten.Line} or [ray]{@link Flatten.Ray}
 */
let Multiline$1 = class Multiline extends LinkedList {
    constructor(...args) {
        super();
        this.isInfinite = false;

        if (args.length === 1 && args[0] instanceof Array && args[0].length > 0) {
            // there may be only one line and
            // only first and last may be rays
            const shapes = args[0];
            const L = shapes.length;
            const anyShape = (s) =>
                s instanceof Flatten.Segment || s instanceof Flatten.Arc ||
                s instanceof Flatten.Ray || s instanceof Flatten.Line;
            const anyShapeExceptLine = (s) =>
                s instanceof Flatten.Segment || s instanceof Flatten.Arc || s instanceof Flatten.Ray;
            const shapeSegmentOrArc = (s) => s instanceof Flatten.Segment || s instanceof Flatten.Arc;
            const validShapes =
                L === 1 && anyShape(shapes[0]) ||
                L > 1 && anyShapeExceptLine(shapes[0]) && anyShapeExceptLine(shapes[L - 1]) &&
                shapes.slice(1, L - 1).every(shapeSegmentOrArc);

            if (validShapes) {
                this.isInfinite = shapes.some(shape =>
                    shape instanceof Flatten.Ray ||
                    shape instanceof Flatten.Line
                );

                for (let shape of shapes) {
                    let edge = new Flatten.Edge(shape);
                    this.append(edge);
                }

                this.setArcLength();
            } else {
                throw Flatten.Errors.ILLEGAL_PARAMETERS;
            }
        }
    }

    /**
     * (Getter) Return array of edges
     * @returns {Edge[]}
     */
    get edges() {
        return [...this];
    }

    /**
     * (Getter) Return bounding box of the multiline
     * @returns {Box}
     */
    get box() {
        return this.edges.reduce( (acc,edge) => acc.merge(edge.box), new Flatten.Box() );
    }

    /**
     * (Getter) Returns array of vertices
     * @returns {Point[]}
     */
    get vertices() {
        let v = this.edges.map(edge => edge.start);
        v.push(this.last.end);
        return v;
    }

    /**
     * (Getter) Returns length of the multiline, return POSITIVE_INFINITY if multiline is infinite
     * @returns {number}
     */
    get length() {
        if (this.isEmpty()) return 0;
        if (this.isInfinite) return Number.POSITIVE_INFINITY;

        let len = 0;
        for (let edge of this) {
            len += edge.length;
        }
        return len
    }

    /**
     * Return new cloned instance of Multiline
     * @returns {Multiline}
     */
    clone() {
        return new Multiline(this.toShapes());
    }

    /**
     * Set arc_length property for each of the edges in the multiline.
     * Arc_length of the edge is the arc length from the multiline start vertex to the edge start vertex
     */
    setArcLength() {
        for (let edge of this) {
            this.setOneEdgeArcLength(edge);
        }
    }

    setOneEdgeArcLength(edge) {
        if (edge === this.first) {
            edge.arc_length = 0.0;
        } else {
            edge.arc_length = edge.prev.arc_length + edge.prev.length;
        }
    }

    /**
     * Return point on multiline at given length from the start of the multiline
     * @param length
     * @returns {Point | null}
     */
    pointAtLength(length) {
        if (length > this.length || length < 0) return null;
        if (this.isInfinite) return null

        let point = null;
        for (let edge of this) {
            if (length >= edge.arc_length &&
                (edge === this.last || length < edge.next.arc_length)) {
                point = edge.pointAtLength(length - edge.arc_length);
                break;
            }
        }
        return point;
    }

    /**
     * Split edge and add new vertex, return new edge inserted
     * @param {Point} pt - point on edge that will be added as new vertex
     * @param {Edge} edge - edge to split
     * @returns {Edge}
     */
    addVertex(pt, edge) {
        let shapes = edge.shape.split(pt);
        // if (shapes.length < 2) return;

        if (shapes[0] === null)   // point incident to edge start vertex, return previous edge
           return edge.prev;

        if (shapes[1] === null)   // point incident to edge end vertex, return edge itself
           return edge;

        let newEdge = new Flatten.Edge(shapes[0]);
        let edgeBefore = edge.prev;

        /* Insert first split edge into linked list after edgeBefore */
        this.insert(newEdge, edgeBefore);     // edge.face ?

        // Update edge shape with second split edge keeping links
        edge.shape = shapes[1];

        return newEdge;
    }

    getChain(edgeFrom, edgeTo) {
        let edges = [];
        for (let edge = edgeFrom; edge !== edgeTo.next; edge = edge.next) {
            edges.push(edge);
        }
        return edges
    }

    /**
     * Split edges of multiline with intersection points and return mutated multiline
     * @param {Point[]} ip - array of points to be added as new vertices
     * @returns {Multiline}
     */
    split(ip) {
        for (let pt of ip) {
            let edge = this.findEdgeByPoint(pt);
            this.addVertex(pt, edge);
        }
        return this;
    }

    /**
     * Returns edge which contains given point
     * @param {Point} pt
     * @returns {Edge}
     */
    findEdgeByPoint(pt) {
        let edgeFound;
        for (let edge of this) {
            if (edge.shape.contains(pt)) {
                edgeFound = edge;
                break;
            }
        }
        return edgeFound;
    }

    /**
     * Calculate distance and shortest segment from any shape to multiline
     * @param shape
     * @returns {[number,Flatten.Segment]}
     */
    distanceTo(shape) {
        if (shape instanceof Point) {
            const [dist, shortest_segment] = Flatten.Distance.shape2multiline(shape, this);
            return [dist, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Line) {
            const [dist, shortest_segment] = Flatten.Distance.shape2multiline(shape, this);
            return [dist, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Circle) {
            const [dist, shortest_segment] = Flatten.Distance.shape2multiline(shape, this);
            return [dist, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Segment) {
            const [dist, shortest_segment] = Flatten.Distance.shape2multiline(shape, this);
            return [dist, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Arc) {
            const [dist, shortest_segment] = Flatten.Distance.shape2multiline(shape, this);
            return [dist, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Multiline) {
            return Flatten.Distance.multiline2multiline(this, shape);
        }

        throw Flatten.Errors.UNSUPPORTED_SHAPE_TYPE;
    }

    /**
     * Calculate intersection of multiline with other shape
     * @param {Shape} shape
     * @returns {Point[]}
     */
    intersect(shape) {
        if (shape instanceof Flatten.Multiline) {
            return intersectMultiline2Multiline(this, shape);
        }
        else {
            return intersectShape2Multiline(shape, this);
        }
    }

    /**
     * Return true if multiline contains the shape: no point of shape lies outside
     * @param shape
     * @returns {boolean}
     */
    contains(shape) {
        if (shape instanceof Flatten.Point) {
            return this.edges.some(edge => edge.shape.contains(shape));
        }

        throw Flatten.Errors.UNSUPPORTED_SHAPE_TYPE;
    }

    /**
     * Returns new multiline translated by vector vec
     * @param {Vector} vec
     * @returns {Multiline}
     */
    translate(vec) {
        return new Multiline(this.edges.map( edge => edge.shape.translate(vec)));
    }

    /**
     * Return new multiline rotated by given angle around given point
     * If point omitted, rotate around origin (0,0)
     * Positive value of angle defines rotation counterclockwise, negative - clockwise
     * @param {number} angle - rotation angle in radians
     * @param {Point} center - rotation center, default is (0,0)
     * @returns {Multiline} - new rotated polygon
     */
    rotate(angle = 0, center = new Flatten.Point()) {
        return new Multiline(this.edges.map( edge => edge.shape.rotate(angle, center) ));
    }

    /**
     * Return new multiline transformed using affine transformation matrix
     * Method does not support unbounded shapes
     * @param {Matrix} matrix - affine transformation matrix
     * @returns {Multiline} - new multiline
     */
    transform(matrix = new Flatten.Matrix()) {
        return new Multiline(this.edges.map( edge => edge.shape.transform(matrix)));
    }

    /**
     * Transform multiline into array of shapes
     * @returns {Shape[]}
     */
    toShapes() {
        return this.edges.map(edge => edge.shape.clone())
    }

    /**
     * This method returns an object that defines how data will be
     * serialized when called JSON.stringify() method
     * @returns {Object}
     */
    toJSON() {
        return this.edges.map(edge => edge.toJSON());
    }

    /**
     * Return string to be inserted into 'points' attribute of <polyline> element
     * @returns {string}
     */
    svgPoints() {
        return this.vertices.map(p => `${p.x},${p.y}`).join(' ')
    }

    /**
     * Return string to be assigned to 'd' attribute of <path> element
     * @returns {string}
     */
    dpath() {
        let dPathStr = `M${this.first.start.x},${this.first.start.y}`;
        for (let edge of this) {
            dPathStr += edge.svg();
        }
        return dPathStr
    }

    /**
     * Return string to draw multiline in svg
     * @param attrs  - an object with attributes for svg path element
     * TODO: support semi-infinite Ray and infinite Line
     * @returns {string}
     */
    svg(attrs = {}) {
        let svgStr = `\n<path ${convertToString({fill: "none", ...attrs})} d="`;
        svgStr += `\nM${this.first.start.x},${this.first.start.y}`;
        for (let edge of this) {
            svgStr += edge.svg();
        }
        svgStr += `" >\n</path>`;
        return svgStr;
    }
};

Flatten.Multiline = Multiline$1;

/**
 * Shortcut function to create multiline
 * @param args
 */
const multiline = (...args) => new Flatten.Multiline(...args);
Flatten.multiline = multiline;

/*
    Smart intersections describe intersection points that refers to the edges they intersect
    This function are supposed for internal usage by morphing and relation methods between
 */

function addToIntPoints(edge, pt, int_points)
{
    let id = int_points.length;
    let shapes = edge.shape.split(pt);

    // if (shapes.length < 2) return;
    if (shapes.length === 0) return;     // Point does not belong to edge ?

    let len = 0;
    if (shapes[0] === null) {   // point incident to edge start vertex
        len = 0;
    }
    else if (shapes[1] === null) {   // point incident to edge end vertex
        len = edge.shape.length;
    }
    else {                             // Edge was split into to edges
        len = shapes[0].length;
    }

    let is_vertex = NOT_VERTEX$1;
    if (EQ(len, 0)) {
        is_vertex |= START_VERTEX$1;
    }
    if (EQ(len, edge.shape.length)) {
        is_vertex |= END_VERTEX$1;
    }
    // Fix intersection point which is end point of the last edge
    let arc_length;
    if (len === Infinity) {
        arc_length = shapes[0].coord(pt);
    }
    else {
        arc_length = (is_vertex & END_VERTEX$1) && edge.next && edge.next.arc_length === 0 ?
            0 :
            edge.arc_length + len;
    }

    int_points.push({
        id: id,
        pt: pt,
        arc_length: arc_length,
        edge_before: edge,
        edge_after: undefined,
        face: edge.face,
        is_vertex: is_vertex
    });
}

function sortIntersections(intersections)
{
    // augment intersections with new sorted arrays
    intersections.int_points1_sorted = getSortedArray(intersections.int_points1);
    intersections.int_points2_sorted = getSortedArray(intersections.int_points2);
}

function getSortedArray(int_points)
{
    let faceMap = new Map;
    let id = 0;
    // Create integer id's for faces
    for (let ip of int_points) {
        if (!faceMap.has(ip.face)) {
            faceMap.set(ip.face, id);
            id++;
        }
    }
    // Augment intersection points with face id's
    for (let ip of int_points) {
        ip.faceId = faceMap.get(ip.face);
    }
    // Clone and sort
    let int_points_sorted = int_points.slice().sort(compareFn);
    return int_points_sorted;
}

function compareFn(ip1, ip2)
{
    // compare face id's
    if (ip1.faceId < ip2.faceId) {
        return -1;
    }
    if (ip1.faceId > ip2.faceId) {
        return 1;
    }
    // same face - compare arc_length
    if (ip1.arc_length < ip2.arc_length) {
        return -1;
    }
    if (ip1.arc_length > ip2.arc_length) {
        return 1;
    }
    return 0;
}

function filterDuplicatedIntersections(intersections)
{
    if (intersections.int_points1.length < 2) return;

    let do_squeeze = false;

    let int_point_ref1;
    let int_point_ref2;
    let int_point_cur1;
    let int_point_cur2;
    for (let i = 0; i < intersections.int_points1_sorted.length; i++) {

        if (intersections.int_points1_sorted[i].id === -1)
            continue;

        int_point_ref1 = intersections.int_points1_sorted[i];
        int_point_ref2 = intersections.int_points2[int_point_ref1.id];

        for (let j=i+1; j < intersections.int_points1_sorted.length; j++) {
            int_point_cur1 = intersections.int_points1_sorted[j];
            if (!EQ(int_point_cur1.arc_length, int_point_ref1.arc_length)) {
                break;
            }
            if (int_point_cur1.id === -1)
                continue;
            int_point_cur2 = intersections.int_points2[int_point_cur1.id];
            if (int_point_cur2.id === -1)
                continue;
            if (int_point_cur1.edge_before === int_point_ref1.edge_before &&
                int_point_cur1.edge_after === int_point_ref1.edge_after &&
                int_point_cur2.edge_before === int_point_ref2.edge_before &&
                int_point_cur2.edge_after === int_point_ref2.edge_after) {
                int_point_cur1.id = -1;
                /* to be deleted */
                int_point_cur2.id = -1;
                /* to be deleted */
                do_squeeze = true;
            }
        }
    }

    int_point_ref2 = intersections.int_points2_sorted[0];
    int_point_ref1 = intersections.int_points1[int_point_ref2.id];
    for (let i = 1; i < intersections.int_points2_sorted.length; i++) {
        let int_point_cur2 = intersections.int_points2_sorted[i];

        if (int_point_cur2.id === -1) continue;
        /* already deleted */

        if (int_point_ref2.id === -1 || /* can't be reference if already deleted */
            !(EQ(int_point_cur2.arc_length, int_point_ref2.arc_length))) {
            int_point_ref2 = int_point_cur2;
            int_point_ref1 = intersections.int_points1[int_point_ref2.id];
            continue;
        }

        let int_point_cur1 = intersections.int_points1[int_point_cur2.id];
        if (int_point_cur1.edge_before === int_point_ref1.edge_before &&
            int_point_cur1.edge_after === int_point_ref1.edge_after &&
            int_point_cur2.edge_before === int_point_ref2.edge_before &&
            int_point_cur2.edge_after === int_point_ref2.edge_after) {
            int_point_cur1.id = -1;
            /* to be deleted */
            int_point_cur2.id = -1;
            /* to be deleted */
            do_squeeze = true;
        }
    }

    if (do_squeeze) {
        intersections.int_points1 = intersections.int_points1.filter((int_point) => int_point.id >= 0);
        intersections.int_points2 = intersections.int_points2.filter((int_point) => int_point.id >= 0);

        // update id's
        intersections.int_points1.forEach((int_point, index) => int_point.id = index);
        intersections.int_points2.forEach((int_point, index) => int_point.id = index);
    }
}

function initializeInclusionFlags(int_points)
{
    for (let int_point of int_points) {
        if (int_point.edge_before) {
            int_point.edge_before.bvStart = undefined;
            int_point.edge_before.bvEnd = undefined;
            int_point.edge_before.bv = undefined;
            int_point.edge_before.overlap = undefined;
        }

        if (int_point.edge_after) {
            int_point.edge_after.bvStart = undefined;
            int_point.edge_after.bvEnd = undefined;
            int_point.edge_after.bv = undefined;
            int_point.edge_after.overlap = undefined;
        }
    }

    for (let int_point of int_points) {
        if (int_point.edge_before) int_point.edge_before.bvEnd = BOUNDARY$1;
        if (int_point.edge_after) int_point.edge_after.bvStart = BOUNDARY$1;
    }
}

function calculateInclusionFlags(int_points, polygon)
{
    for (let int_point of int_points) {
        if (int_point.edge_before) int_point.edge_before.setInclusion(polygon);
        if (int_point.edge_after) int_point.edge_after.setInclusion(polygon);
    }
}

function setOverlappingFlags(intersections)
{
    let cur_face = undefined;
    let first_int_point_in_face_id = undefined;
    let next_int_point1 = undefined;
    let num_int_points = intersections.int_points1.length;

    for (let i = 0; i < num_int_points; i++) {
        let cur_int_point1 = intersections.int_points1_sorted[i];

        // Find boundary chain in the polygon1
        if (cur_int_point1.face !== cur_face) {                               // next chain started
            first_int_point_in_face_id = i; // cur_int_point1;
            cur_face = cur_int_point1.face;
        }

        // Skip duplicated points with same <x,y> in "cur_int_point1" pool
        let int_points_cur_pool_start = i;
        let int_points_cur_pool_num = intPointsPoolCount(intersections.int_points1_sorted, i, cur_face);
        let next_int_point_id;
        if (int_points_cur_pool_start + int_points_cur_pool_num < num_int_points &&
            intersections.int_points1_sorted[int_points_cur_pool_start + int_points_cur_pool_num].face === cur_face) {
            next_int_point_id = int_points_cur_pool_start + int_points_cur_pool_num;
        } else {                                         // get first point from the same face
            next_int_point_id = first_int_point_in_face_id;
        }

        // From all points with same ,x,y. in 'next_int_point1' pool choose one that
        // has same face both in res_poly and in wrk_poly
        let int_points_next_pool_num = intPointsPoolCount(intersections.int_points1_sorted, next_int_point_id, cur_face);
        next_int_point1 = null;
        for (let j=next_int_point_id; j < next_int_point_id + int_points_next_pool_num; j++) {
            let next_int_point1_tmp = intersections.int_points1_sorted[j];
            if (next_int_point1_tmp.face === cur_face &&
                intersections.int_points2[next_int_point1_tmp.id].face === intersections.int_points2[cur_int_point1.id].face) {
                next_int_point1 = next_int_point1_tmp;
                break;
            }
        }
        if (next_int_point1 === null)
            continue;

        let edge_from1 = cur_int_point1.edge_after;
        let edge_to1 = next_int_point1.edge_before;

        if (!(edge_from1.bv === BOUNDARY$1 && edge_to1.bv === BOUNDARY$1))      // not a boundary chain - skip
            continue;

        if (edge_from1 !== edge_to1)                    //  one edge chain    TODO: support complex case
            continue;

        /* Find boundary chain in polygon2 between same intersection points */
        let cur_int_point2 = intersections.int_points2[cur_int_point1.id];
        let next_int_point2 = intersections.int_points2[next_int_point1.id];

        let edge_from2 = cur_int_point2.edge_after;
        let edge_to2 = next_int_point2.edge_before;

        /* if [edge_from2..edge_to2] is not a boundary chain, invert it */
        /* check also that chain consist of one or two edges */
        if (!(edge_from2.bv === BOUNDARY$1 && edge_to2.bv === BOUNDARY$1 && edge_from2 === edge_to2)) {
            cur_int_point2 = intersections.int_points2[next_int_point1.id];
            next_int_point2 = intersections.int_points2[cur_int_point1.id];

            edge_from2 = cur_int_point2.edge_after;
            edge_to2 = next_int_point2.edge_before;
        }

        if (!(edge_from2.bv === BOUNDARY$1 && edge_to2.bv === BOUNDARY$1 && edge_from2 === edge_to2))
            continue;                           // not an overlapping chain - skip   TODO: fix boundary conflict

        // Set overlapping flag - one-to-one case
        edge_from1.setOverlap(edge_from2);
    }
}

function intPointsPoolCount(int_points, cur_int_point_num, cur_face)
{
    let int_point_current;
    let int_point_next;

    let int_points_pool_num = 1;

    if (int_points.length === 1) return 1;

    int_point_current = int_points[cur_int_point_num];

    for (let i = cur_int_point_num + 1; i < int_points.length; i++) {
        if (int_point_current.face !== cur_face) {      /* next face started */
            break;
        }

        int_point_next = int_points[i];

        if (!(int_point_next.pt.equalTo(int_point_current.pt) &&
            int_point_next.edge_before === int_point_current.edge_before &&
            int_point_next.edge_after === int_point_current.edge_after)) {
            break;         /* next point is different - break and exit */
        }

        int_points_pool_num++;     /* duplicated intersection point - increase counter */
    }
    return int_points_pool_num;
}

function splitByIntersections(polygon, int_points)
{
    if (!int_points) return;
    for (let int_point of int_points) {
        let edge = int_point.edge_before;

        // recalculate vertex flag: it may be changed after previous split
        int_point.is_vertex = NOT_VERTEX$1;
        if (edge.shape.start && edge.shape.start.equalTo(int_point.pt)) {
            int_point.is_vertex |= START_VERTEX$1;
        }
        if (edge.shape.end && edge.shape.end.equalTo(int_point.pt)) {
            int_point.is_vertex |= END_VERTEX$1;
        }

        if (int_point.is_vertex & START_VERTEX$1) {    // nothing to split
            int_point.edge_before = edge.prev;
            if (edge.prev) {
                int_point.is_vertex = END_VERTEX$1;   // polygon
            }
            continue;
        }
        if (int_point.is_vertex & END_VERTEX$1) {    // nothing to split
            continue;
        }

        let newEdge = polygon.addVertex(int_point.pt, edge);
        int_point.edge_before = newEdge;
    }

    for (let int_point of int_points) {
        if (int_point.edge_before) {
            int_point.edge_after = int_point.edge_before.next;
        }
        else {
            if (polygon instanceof Multiline$1 && int_point.is_vertex & START_VERTEX$1) {
                int_point.edge_after = polygon.first;
            }
        }
    }
}

function insertBetweenIntPoints(int_point1, int_point2, new_edges) {
    const edge_before = int_point1.edge_before;
    const edge_after = int_point2.edge_after;
    const len = new_edges.length;
    edge_before.next = new_edges[0];
    new_edges[0].prev = edge_before;

    new_edges[len-1].next = edge_after;
    edge_after.prev = new_edges[len-1];
}

/**
 * Created by Alex Bol on 12/02/2018.
 */
/**
 * @module BooleanOperations
 */

const {INSIDE: INSIDE$1, OUTSIDE, BOUNDARY, OVERLAP_SAME, OVERLAP_OPPOSITE} = Constants;
const {NOT_VERTEX, START_VERTEX, END_VERTEX} = Constants;

const BOOLEAN_UNION = 1;
const BOOLEAN_INTERSECT = 2;
const BOOLEAN_SUBTRACT = 3;


/**
 * Unify two polygons polygons and returns new polygon. <br/>
 * Point belongs to the resulted polygon if it belongs to the first OR to the second polygon
 * @param {Polygon} polygon1 - first operand
 * @param {Polygon} polygon2 - second operand
 * @returns {Polygon}
 */
function unify(polygon1, polygon2) {
    let [res_poly, wrk_poly] = booleanOpBinary(polygon1, polygon2, BOOLEAN_UNION, true);
    return res_poly;
}

/**
 * Subtract second polygon from the first and returns new polygon
 * Point belongs to the resulted polygon if it belongs to the first polygon AND NOT to the second polygon
 * @param {Polygon} polygon1 - first operand
 * @param {Polygon} polygon2 - second operand
 * @returns {Polygon}
 */
function subtract(polygon1, polygon2) {
    let polygon2_tmp = polygon2.clone();
    let polygon2_reversed = polygon2_tmp.reverse();
    let [res_poly, wrk_poly] = booleanOpBinary(polygon1, polygon2_reversed, BOOLEAN_SUBTRACT, true);
    return res_poly;
}

/**
 * Intersect two polygons and returns new polygon
 * Point belongs to the resulted polygon is it belongs to the first AND to the second polygon
 * @param {Polygon} polygon1 - first operand
 * @param {Polygon} polygon2 - second operand
 * @returns {Polygon}
 */
function intersect$1(polygon1, polygon2) {
    let [res_poly, wrk_poly] = booleanOpBinary(polygon1, polygon2, BOOLEAN_INTERSECT, true);
    return res_poly;
}

/**
 * Returns boundary of intersection between two polygons as two arrays of shapes (Segments/Arcs) <br/>
 * The first array are shapes from the first polygon, the second array are shapes from the second
 * @param {Polygon} polygon1 - first operand
 * @param {Polygon} polygon2 - second operand
 * @returns {Shape[][]}
 */
function innerClip(polygon1, polygon2) {
    let [res_poly, wrk_poly] = booleanOpBinary(polygon1, polygon2, BOOLEAN_INTERSECT, false);

    let clip_shapes1 = [];
    for (let face of res_poly.faces) {
        clip_shapes1 = [...clip_shapes1, ...[...face.edges].map(edge => edge.shape)];
    }
    let clip_shapes2 = [];
    for (let face of wrk_poly.faces) {
        clip_shapes2 = [...clip_shapes2, ...[...face.edges].map(edge => edge.shape)];
    }
    return [clip_shapes1, clip_shapes2];
}

/**
 * Returns boundary of subtraction of the second polygon from first polygon as array of shapes
 * @param {Polygon} polygon1 - first operand
 * @param {Polygon} polygon2 - second operand
 * @returns {Shape[]}
 */
function outerClip(polygon1, polygon2) {
    let [res_poly, wrk_poly] = booleanOpBinary(polygon1, polygon2, BOOLEAN_SUBTRACT, false);

    let clip_shapes1 = [];
    for (let face of res_poly.faces) {
        clip_shapes1 = [...clip_shapes1, ...[...face.edges].map(edge => edge.shape)];
    }

    return clip_shapes1;
}

/**
 * Returns intersection points between boundaries of two polygons as two array of points <br/>
 * Points in the first array belong to first polygon, points from the second - to the second.
 * Points in each array are ordered according to the direction of the correspondent polygon
 * @param {Polygon} polygon1 - first operand
 * @param {Polygon} polygon2 - second operand
 * @returns {Point[][]}
 */
function calculateIntersections(polygon1, polygon2) {
    let res_poly = polygon1.clone();
    let wrk_poly = polygon2.clone();

    // get intersection points
    let intersections = getIntersections(res_poly, wrk_poly);

    // sort intersection points
    sortIntersections(intersections);

    // split by intersection points
    splitByIntersections(res_poly, intersections.int_points1_sorted);
    splitByIntersections(wrk_poly, intersections.int_points2_sorted);

    // filter duplicated intersection points
    filterDuplicatedIntersections(intersections);

    // sort intersection points again after filtering
    sortIntersections(intersections);

    let ip_sorted1 = intersections.int_points1_sorted.map( int_point => int_point.pt);
    let ip_sorted2 = intersections.int_points2_sorted.map( int_point => int_point.pt);
    return [ip_sorted1, ip_sorted2];
}

function filterNotRelevantEdges(res_poly, wrk_poly, intersections, op) {
    // keep not intersected faces for further remove and merge
    let notIntersectedFacesRes = getNotIntersectedFaces(res_poly, intersections.int_points1);
    let notIntersectedFacesWrk = getNotIntersectedFaces(wrk_poly, intersections.int_points2);

    // calculate inclusion flag for not intersected faces
    calcInclusionForNotIntersectedFaces(notIntersectedFacesRes, wrk_poly);
    calcInclusionForNotIntersectedFaces(notIntersectedFacesWrk, res_poly);

    // initialize inclusion flags for edges incident to intersections
    initializeInclusionFlags(intersections.int_points1);
    initializeInclusionFlags(intersections.int_points2);

    // calculate inclusion flags only for edges incident to intersections
    calculateInclusionFlags(intersections.int_points1, wrk_poly);
    calculateInclusionFlags(intersections.int_points2, res_poly);

    // fix boundary conflicts
    while (fixBoundaryConflicts(res_poly, wrk_poly, intersections.int_points1, intersections.int_points1_sorted, intersections.int_points2, intersections));
    // while (fixBoundaryConflicts(wrk_poly, res_poly, intersections.int_points2, intersections.int_points2_sorted, intersections.int_points1, intersections));

    // Set overlapping flags for boundary chains: SAME or OPPOSITE
    setOverlappingFlags(intersections);

    // remove not relevant chains between intersection points
    removeNotRelevantChains(res_poly, op, intersections.int_points1_sorted, true);
    removeNotRelevantChains(wrk_poly, op, intersections.int_points2_sorted, false);

    // remove not relevant not intersected faces from res_polygon and wrk_polygon
    // if op == UNION, remove faces that are included in wrk_polygon without intersection
    // if op == INTERSECT, remove faces that are not included into wrk_polygon
    removeNotRelevantNotIntersectedFaces(res_poly, notIntersectedFacesRes, op, true);
    removeNotRelevantNotIntersectedFaces(wrk_poly, notIntersectedFacesWrk, op, false);
}

function swapLinksAndRestore(res_poly, wrk_poly, intersections, op) {

    // add edges of wrk_poly into the edge container of res_poly
    copyWrkToRes(res_poly, wrk_poly, op, intersections.int_points2);

    // swap links from res_poly to wrk_poly and vice versa
    swapLinks(res_poly, wrk_poly, intersections);

    // remove old faces
    removeOldFaces(res_poly, intersections.int_points1);
    removeOldFaces(wrk_poly, intersections.int_points2);

    // restore faces
    restoreFaces(res_poly, intersections.int_points1, intersections.int_points2);
    restoreFaces(res_poly, intersections.int_points2, intersections.int_points1);

    // merge relevant not intersected faces from wrk_polygon to res_polygon
    // mergeRelevantNotIntersectedFaces(res_poly, wrk_poly);
}


function booleanOpBinary(polygon1, polygon2, op, restore)
{
    let res_poly = polygon1.clone();
    let wrk_poly = polygon2.clone();

    // get intersection points
    let intersections = getIntersections(res_poly, wrk_poly);

    // sort intersection points
    sortIntersections(intersections);

    // split by intersection points
    splitByIntersections(res_poly, intersections.int_points1_sorted);
    splitByIntersections(wrk_poly, intersections.int_points2_sorted);

    // filter duplicated intersection points
    filterDuplicatedIntersections(intersections);

    // sort intersection points again after filtering
    sortIntersections(intersections);

    // calculate inclusion and remove not relevant edges
    filterNotRelevantEdges(res_poly, wrk_poly, intersections, op);

    if (restore) {
        swapLinksAndRestore(res_poly, wrk_poly, intersections, op);
    }

    return [res_poly, wrk_poly];
}

function getIntersections(polygon1, polygon2)
{
    let intersections = {
        int_points1: [],
        int_points2: []
    };

    // calculate intersections
    for (let edge1 of polygon1.edges) {

        // request edges of polygon2 in the box of edge1
        let resp = polygon2.edges.search(edge1.box);

        // for each edge2 in response
        for (let edge2 of resp) {

            // calculate intersections between edge1 and edge2
            let ip = edge1.shape.intersect(edge2.shape);

            // for each intersection point
            for (let pt of ip) {
                addToIntPoints(edge1, pt, intersections.int_points1);
                addToIntPoints(edge2, pt, intersections.int_points2);
            }
        }
    }
    return intersections;
}

function getNotIntersectedFaces(poly, int_points)
{
    let notIntersected = [];
    for (let face of poly.faces) {
        if (!int_points.find((ip) => ip.face === face)) {
            notIntersected.push(face);
        }
    }
    return notIntersected;
}

function calcInclusionForNotIntersectedFaces(notIntersectedFaces, poly2)
{
    for (let face of notIntersectedFaces) {
        face.first.bv = face.first.bvStart = face.first.bvEnd = undefined;
        face.first.setInclusion(poly2);
    }
}

function fixBoundaryConflicts(poly1, poly2, int_points1, int_points1_sorted, int_points2, intersections )
{
    let cur_face;
    let first_int_point_in_face_id;
    let next_int_point1;
    let num_int_points = int_points1_sorted.length;
    let iterate_more = false;

    for (let i = 0; i < num_int_points; i++) {
        let cur_int_point1 = int_points1_sorted[i];

        // Find boundary chain in the polygon1
        if (cur_int_point1.face !== cur_face) {                               // next chain started
            first_int_point_in_face_id = i; // cur_int_point1;
            cur_face = cur_int_point1.face;
        }

        // Skip duplicated points with same <x,y> in "cur_int_point1" pool
        let int_points_cur_pool_start = i;
        let int_points_cur_pool_num = intPointsPoolCount(int_points1_sorted, i, cur_face);
        let next_int_point_id;
        if (int_points_cur_pool_start + int_points_cur_pool_num < num_int_points &&
            int_points1_sorted[int_points_cur_pool_start + int_points_cur_pool_num].face === cur_face) {
            next_int_point_id = int_points_cur_pool_start + int_points_cur_pool_num;
        } else {                                         // get first point from the same face
            next_int_point_id = first_int_point_in_face_id;
        }

        // From all points with same ,x,y. in 'next_int_point1' pool choose one that
        // has same face both in res_poly and in wrk_poly
        let int_points_next_pool_num = intPointsPoolCount(int_points1_sorted, next_int_point_id, cur_face);
        next_int_point1 = null;
        for (let j=next_int_point_id; j < next_int_point_id + int_points_next_pool_num; j++) {
            let next_int_point1_tmp = int_points1_sorted[j];
            if (next_int_point1_tmp.face === cur_face &&
                int_points2[next_int_point1_tmp.id].face === int_points2[cur_int_point1.id].face) {
                next_int_point1 = next_int_point1_tmp;
                break;
            }
        }
        if (next_int_point1 === null)
            continue;

        let edge_from1 = cur_int_point1.edge_after;
        let edge_to1 = next_int_point1.edge_before;

        // Case #1. One of the ends is not boundary - probably tiny edge wrongly marked as boundary
        if (edge_from1.bv === BOUNDARY && edge_to1.bv != BOUNDARY) {
            edge_from1.bv = edge_to1.bv;
            continue;
        }

        if (edge_from1.bv != BOUNDARY && edge_to1.bv === BOUNDARY) {
            edge_to1.bv = edge_from1.bv;
            continue;
        }

        // Set up all boundary values for middle edges. Need for cases 2 and 3
        if ( (edge_from1.bv === BOUNDARY && edge_to1.bv === BOUNDARY && edge_from1 != edge_to1) ||
        (edge_from1.bv === INSIDE$1 && edge_to1.bv === OUTSIDE  || edge_from1.bv === OUTSIDE && edge_to1.bv === INSIDE$1 ) ) {
            let edge_tmp = edge_from1.next;
            while (edge_tmp != edge_to1) {
                edge_tmp.bvStart = undefined;
                edge_tmp.bvEnd = undefined;
                edge_tmp.bv = undefined;
                edge_tmp.setInclusion(poly2);
                edge_tmp = edge_tmp.next;
            }
        }

        // Case #2. Both of the ends boundary. Check all the edges in the middle
        // If some edges in the middle are not boundary then update bv of 'from' and 'to' edges
        if (edge_from1.bv === BOUNDARY && edge_to1.bv === BOUNDARY && edge_from1 != edge_to1) {
            let edge_tmp = edge_from1.next;
            let new_bv;
            while (edge_tmp != edge_to1) {
                if (edge_tmp.bv != BOUNDARY) {
                    if (new_bv === undefined) {        // first not boundary edge between from and to
                        new_bv = edge_tmp.bv;
                    }
                    else {                            // another not boundary edge between from and to
                        if (edge_tmp.bv != new_bv) {  // and it has different bv - can't resolve conflict
                            throw Errors.UNRESOLVED_BOUNDARY_CONFLICT;
                        }
                    }
                }
                edge_tmp = edge_tmp.next;
            }

            if (new_bv != undefined) {
                edge_from1.bv = new_bv;
                edge_to1.bv = new_bv;
            }
            continue;         // all middle edges are boundary, proceed with this
        }

        // Case 3. One of the ends is inner, another is outer
        if (edge_from1.bv === INSIDE$1 && edge_to1.bv === OUTSIDE  || edge_from1.bv === OUTSIDE && edge_to1.bv === INSIDE$1 ) {
            let edge_tmp = edge_from1;
            // Find missing intersection point
            while (edge_tmp != edge_to1) {
                if (edge_tmp.bvStart === edge_from1.bv && edge_tmp.bvEnd === edge_to1.bv) {
                    let [dist, segment] = edge_tmp.shape.distanceTo(poly2);
                    if (dist < 10*Flatten.DP_TOL) {  // it should be very close
                        // let pt = edge_tmp.end;
                        // add to the list of intersections of poly1
                        addToIntPoints(edge_tmp, segment.ps, int_points1);

                        // split edge_tmp in poly1 if need
                        let int_point1 = int_points1[int_points1.length-1];
                        if (int_point1.is_vertex & START_VERTEX) {        // nothing to split
                            int_point1.edge_after = edge_tmp;
                            int_point1.edge_before = edge_tmp.prev;
                            edge_tmp.bvStart = BOUNDARY;
                            edge_tmp.bv = undefined;
                            edge_tmp.setInclusion(poly2);
                        }
                        else if (int_point1.is_vertex & END_VERTEX) {    // nothing to split
                            int_point1.edge_after = edge_tmp.next;
                            edge_tmp.bvEnd = BOUNDARY;
                            edge_tmp.bv = undefined;
                            edge_tmp.setInclusion(poly2);
                        }
                        else {        // split edge here
                            let newEdge1 = poly2.addVertex(int_point1.pt, edge_tmp);
                            int_point1.edge_before = newEdge1;
                            int_point1.edge_after = newEdge1.next;

                            newEdge1.setInclusion(poly2);

                            newEdge1.next.bvStart = BOUNDARY;
                            newEdge1.next.bvEnd = undefined;
                            newEdge1.next.bv = undefined;
                            newEdge1.next.setInclusion(poly2);
                        }

                        // add to the list of intersections of poly2
                        let edge2 = poly2.findEdgeByPoint(segment.pe);
                        addToIntPoints(edge2, segment.pe, int_points2);
                        // split edge2 in poly2 if need
                        let int_point2 = int_points2[int_points2.length-1];
                        if (int_point2.is_vertex & START_VERTEX) {        // nothing to split
                            int_point2.edge_after = edge2;
                            int_point2.edge_before = edge2.prev;
                        }
                        else if (int_point2.is_vertex & END_VERTEX) {    // nothing to split
                            int_point2.edge_after = edge2.next;
                        }
                        else {        // split edge here
                            // first locate int_points that may refer to edge2 as edge.after
                            // let int_point2_edge_before = int_points2.find( int_point => int_point.edge_before === edge2)
                            let int_point2_edge_after = int_points2.find( int_point => int_point.edge_after === edge2 );

                            let newEdge2 = poly2.addVertex(int_point2.pt, edge2);
                            int_point2.edge_before = newEdge2;
                            int_point2.edge_after = newEdge2.next;

                            if (int_point2_edge_after)
                                int_point2_edge_after.edge_after = newEdge2;

                            newEdge2.bvStart = undefined;
                            newEdge2.bvEnd = BOUNDARY;
                            newEdge2.bv = undefined;
                            newEdge2.setInclusion(poly1);

                            newEdge2.next.bvStart = BOUNDARY;
                            newEdge2.next.bvEnd = undefined;
                            newEdge2.next.bv = undefined;
                            newEdge2.next.setInclusion(poly1);
                        }

                        sortIntersections(intersections);

                        iterate_more = true;
                        break;
                    }
                }
                edge_tmp = edge_tmp.next;
            }

            // we changed intersections inside loop, have to exit and repair again
            if (iterate_more)
                break;

            throw Errors.UNRESOLVED_BOUNDARY_CONFLICT;
        }
    }

    return iterate_more;
}

function removeNotRelevantChains(polygon, op, int_points, is_res_polygon)
{
    if (!int_points) return;
    let cur_face = undefined;
    let first_int_point_in_face_num = undefined;
    let int_point_current;
    let int_point_next;

    for (let i = 0; i < int_points.length; i++) {
        int_point_current = int_points[i];

        if (int_point_current.face !== cur_face) {   // next face started
            first_int_point_in_face_num = i;
            cur_face = int_point_current.face;
        }

        if (cur_face.isEmpty())                // ??
            continue;

        // Get next int point from the same face that current

        // Count how many duplicated points with same <x,y> in "points from" pool ?
        let int_points_from_pull_start = i;
        let int_points_from_pull_num = intPointsPoolCount(int_points, i, cur_face);
        let next_int_point_num;
        if (int_points_from_pull_start + int_points_from_pull_num < int_points.length &&
            int_points[int_points_from_pull_start + int_points_from_pull_num].face === int_point_current.face) {
            next_int_point_num = int_points_from_pull_start + int_points_from_pull_num;
        } else {                                         // get first point from the same face
            next_int_point_num = first_int_point_in_face_num;
        }
        int_point_next = int_points[next_int_point_num];

        /* Count how many duplicated points with same <x,y> in "points to" pull ? */
        let int_points_to_pull_start = next_int_point_num;
        let int_points_to_pull_num = intPointsPoolCount(int_points, int_points_to_pull_start, cur_face);


        let edge_from = int_point_current.edge_after;
        let edge_to = int_point_next.edge_before;

        if ((edge_from.bv === INSIDE$1 && edge_to.bv === INSIDE$1 && op === BOOLEAN_UNION) ||
            (edge_from.bv === OUTSIDE && edge_to.bv === OUTSIDE && op === BOOLEAN_INTERSECT) ||
            ((edge_from.bv === OUTSIDE || edge_to.bv === OUTSIDE) && op === BOOLEAN_SUBTRACT && !is_res_polygon) ||
            ((edge_from.bv === INSIDE$1 || edge_to.bv === INSIDE$1) && op === BOOLEAN_SUBTRACT && is_res_polygon) ||
            (edge_from.bv === BOUNDARY && edge_to.bv === BOUNDARY && (edge_from.overlap & OVERLAP_SAME) && is_res_polygon) ||
            (edge_from.bv === BOUNDARY && edge_to.bv === BOUNDARY && (edge_from.overlap & OVERLAP_OPPOSITE))) {

            polygon.removeChain(cur_face, edge_from, edge_to);

            /* update all points in "points from" pull */
            for (let k = int_points_from_pull_start; k < int_points_from_pull_start + int_points_from_pull_num; k++) {
                int_points[k].edge_after = undefined;
            }

            /* update all points in "points to" pull */
            for (let k = int_points_to_pull_start; k < int_points_to_pull_start + int_points_to_pull_num; k++) {
                int_points[k].edge_before = undefined;
            }
        }

        /* skip to the last point in "points from" group */
        i += int_points_from_pull_num - 1;
    }
}
function copyWrkToRes(res_polygon, wrk_polygon, op, int_points)
{
    for (let face of wrk_polygon.faces) {
        for (let edge of face) {
            res_polygon.edges.add(edge);
        }
        // If union - add face from wrk_polygon that is not intersected with res_polygon
        if ( /*(op === BOOLEAN_UNION || op == BOOLEAN_SUBTRACT) &&*/
            int_points.find((ip) => (ip.face === face)) === undefined) {
            res_polygon.addFace(face.first, face.last);
        }
    }
}

function swapLinks(res_polygon, wrk_polygon, intersections)
{
    if (intersections.int_points1.length === 0) return;

    for (let i = 0; i < intersections.int_points1.length; i++) {
        let int_point1 = intersections.int_points1[i];
        let int_point2 = intersections.int_points2[i];

        // Simple case - find continuation on the other polygon

        // Process edge from res_polygon
        if (int_point1.edge_before !== undefined && int_point1.edge_after === undefined) {    // swap need
            if (int_point2.edge_before === undefined && int_point2.edge_after !== undefined) {  // simple case
                // Connect edges
                int_point1.edge_before.next = int_point2.edge_after;
                int_point2.edge_after.prev = int_point1.edge_before;

                // Fill in missed links in intersection points
                int_point1.edge_after = int_point2.edge_after;
                int_point2.edge_before = int_point1.edge_before;
            }
        }
        // Process edge from wrk_polygon
        if (int_point2.edge_before !== undefined && int_point2.edge_after === undefined) {    // swap need
            if (int_point1.edge_before === undefined && int_point1.edge_after !== undefined) {  // simple case
                // Connect edges
                int_point2.edge_before.next = int_point1.edge_after;
                int_point1.edge_after.prev = int_point2.edge_before;

                // Complete missed links
                int_point2.edge_after = int_point1.edge_after;
                int_point1.edge_before = int_point2.edge_before;
            }
        }

        // Continuation not found - complex case
        // Continuation will be found on the same polygon.
        // It happens when intersection point is actually touching point
        // Polygon1
        if (int_point1.edge_before !== undefined && int_point1.edge_after === undefined) {    // still swap need
            for (let int_point of intersections.int_points1_sorted) {
                if (int_point === int_point1) continue;     // skip same
                if (int_point.edge_before === undefined && int_point.edge_after !== undefined) {
                    if (int_point.pt.equalTo(int_point1.pt)) {
                        // Connect edges
                        int_point1.edge_before.next = int_point.edge_after;
                        int_point.edge_after.prev = int_point1.edge_before;

                        // Complete missed links
                        int_point1.edge_after = int_point.edge_after;
                        int_point.edge_before = int_point1.edge_before;
                    }
                }
            }
        }
        // Polygon2
        if (int_point2.edge_before !== undefined && int_point2.edge_after === undefined) {    // still swap need
            for (let int_point of intersections.int_points2_sorted) {
                if (int_point === int_point2) continue;     // skip same
                if (int_point.edge_before === undefined && int_point.edge_after !== undefined) {
                    if (int_point.pt.equalTo(int_point2.pt)) {
                        // Connect edges
                        int_point2.edge_before.next = int_point.edge_after;
                        int_point.edge_after.prev = int_point2.edge_before;

                        // Complete missed links
                        int_point2.edge_after = int_point.edge_after;
                        int_point.edge_before = int_point2.edge_before;
                    }
                }
            }
        }
    }
    // Sanity check that no dead ends left
}

function removeOldFaces(polygon, int_points)
{
    for (let int_point of int_points) {
        polygon.faces.delete(int_point.face);
        int_point.face = undefined;
        if (int_point.edge_before)
            int_point.edge_before.face = undefined;
        if (int_point.edge_after)
            int_point.edge_after.face = undefined;
    }
}

function restoreFaces(polygon, int_points, other_int_points)
{
    // For each intersection point - create new face
    for (let int_point of int_points) {
        if (int_point.edge_before === undefined || int_point.edge_after === undefined)  // completely deleted
            continue;
        if (int_point.face)            // already restored
            continue;

        if (int_point.edge_after.face || int_point.edge_before.face)        // Face already created. Possible case in duplicated intersection points
            continue;

        let first = int_point.edge_after;      // face start
        let last = int_point.edge_before;      // face end;

        try {
            LinkedList.testInfiniteLoop(first);    // check and throw error if infinite loop found
        }
        catch (error) {
            throw Errors.CANNOT_COMPLETE_BOOLEAN_OPERATION
        }

        let face = polygon.addFace(first, last);

        // Mark intersection points from the newly create face
        // to avoid multiple creation of the same face.
        // Face was assigned to each edge of new face in addFace function
        for (let int_point_tmp of int_points) {
            if (int_point_tmp.edge_before && int_point_tmp.edge_after &&
                int_point_tmp.edge_before.face === face && int_point_tmp.edge_after.face === face) {
                int_point_tmp.face = face;
            }
        }
        // Mark other intersection points as well
        for (let int_point_tmp of other_int_points) {
            if (int_point_tmp.edge_before && int_point_tmp.edge_after &&
                int_point_tmp.edge_before.face === face && int_point_tmp.edge_after.face === face) {
                int_point_tmp.face = face;
            }
        }
    }
}

function removeNotRelevantNotIntersectedFaces(polygon, notIntersectedFaces, op, is_res_polygon)
{
    for (let face of notIntersectedFaces) {
        let rel = face.first.bv;
        if (op === BOOLEAN_UNION && rel === INSIDE$1 ||
            op === BOOLEAN_SUBTRACT && rel === INSIDE$1 && is_res_polygon ||
            op === BOOLEAN_SUBTRACT && rel === OUTSIDE && !is_res_polygon ||
            op === BOOLEAN_INTERSECT && rel === OUTSIDE) {

            polygon.deleteFace(face);
        }
    }
}

var BooleanOperations = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BOOLEAN_INTERSECT: BOOLEAN_INTERSECT,
    BOOLEAN_SUBTRACT: BOOLEAN_SUBTRACT,
    BOOLEAN_UNION: BOOLEAN_UNION,
    calculateIntersections: calculateIntersections,
    innerClip: innerClip,
    intersect: intersect$1,
    outerClip: outerClip,
    removeNotRelevantChains: removeNotRelevantChains,
    removeOldFaces: removeOldFaces,
    restoreFaces: restoreFaces,
    subtract: subtract,
    unify: unify
});

/*
    Dimensionally extended 9-intersected model
    See https://en.wikipedia.org/wiki/DE-9IM for more details
 */
// const DISJOINT = RegExp('FF.FF....');
const EQUAL = RegExp('T.F..FFF.|T.F...F..');
const INTERSECT = RegExp('T........|.T.......|...T.....|....T....');
const TOUCH = RegExp('FT.......|F..T.....|F...T....');
const INSIDE = RegExp('T.F..F...');
const COVERED = RegExp('T.F..F...|.TF..F...|..FT.F...|..F.TF...');

class DE9IM {
    /**
     * Create new instance of DE9IM matrix
     */
    constructor() {
        /**
         * Array representing 3x3 intersection matrix
         * @type {Shape[]}
         */
        this.m = new Array(9).fill(undefined);
    }

    /**
     * Get Interior To Interior intersection
     * @returns {Shape[] | undefined}
     */
    get I2I() {
        return this.m[0];
    }

    /**
     * Set Interior To Interior intersection
     * @param geom
     */
    set I2I(geom) {
        this.m[0] = geom;
    }

    /**
     * Get Interior To Boundary intersection
     * @returns {Shape[] | undefined}
     */
    get I2B() {
        return this.m[1];
    }

    /**
     * Set Interior to Boundary intersection
     * @param geomc
     */
    set I2B(geom) {
        this.m[1] = geom;
    }

    /**
     * Get Interior To Exterior intersection
     * @returns {Shape[] | undefined}
     */
    get I2E() {
        return this.m[2];
    }

    /**
     * Set Interior to Exterior intersection
     * @param geom
     */
    set I2E(geom) {
        this.m[2] = geom;
    }

    /**
     * Get Boundary To Interior intersection
     * @returns {Shape[] | undefined}
     */
    get B2I() {
        return this.m[3];
    }

    /**
     * Set Boundary to Interior intersection
     * @param geom
     */
    set B2I(geom) {
        this.m[3] = geom;
    }

    /**
     * Get Boundary To Boundary intersection
     * @returns {Shape[] | undefined}
     */
    get B2B() {
        return this.m[4];
    }

    /**
     * Set Boundary to Boundary intersection
     * @param geom
     */
    set B2B(geom) {
        this.m[4] = geom;
    }

    /**
     * Get Boundary To Exterior intersection
     * @returns {Shape[] | undefined}
     */
    get B2E() {
        return this.m[5];
    }

    /**
     * Set Boundary to Exterior intersection
     * @param geom
     */
    set B2E(geom) {
        this.m[5] = geom;
    }

    /**
     * Get Exterior To Interior intersection
     * @returns {Shape[] | undefined}
     */
    get E2I() {
        return this.m[6];
    }

    /**
     * Set Exterior to Interior intersection
     * @param geom
     */
    set E2I(geom) {
        this.m[6] = geom;
    }

    /**
     * Get Exterior To Boundary intersection
     * @returns {Shape[] | undefined}
     */
    get E2B() {
        return this.m[7];
    }

    /**
     * Set Exterior to Boundary intersection
     * @param geom
     */
    set E2B(geom) {
        this.m[7] = geom;
    }

    /**
     * Get Exterior to Exterior intersection
     * @returns {Shape[] | undefined}
     */
    get E2E() {
        return this.m[8];
    }

    /**
     * Set Exterior to Exterior intersection
     * @param geom
     */
    set E2E(geom) {
        this.m[8] = geom;
    }

    /**
     * Return de9im matrix as string where<br/>
     * - intersection is 'T'<br/>
     * - not intersected is 'F'<br/>
     * - not relevant is '*'<br/>
     * For example, string 'FF**FF****' means 'DISJOINT'
     * @returns {string}
     */
    toString() {
        return this.m.map( e => {
            if (e instanceof Array && e.length > 0) {
                return 'T'
            }
            else if (e instanceof Array && e.length === 0) {
                return 'F'
            }
            else {
                return '*'
            }
        }).join("")
    }

    equal() {
        return EQUAL.test(this.toString());
    }

    intersect() {
        return INTERSECT.test(this.toString());
    }

    touch() {
        return TOUCH.test(this.toString());
    }

    inside() {
        return INSIDE.test(this.toString());
    }

    covered() {
        return COVERED.test(this.toString());
    }
}

/**
 * @module RayShoot
 */
/**
 * Implements ray shooting algorithm. Returns relation between point and polygon: inside, outside or boundary
 * @param {Polygon} polygon - polygon to test
 * @param {Point} point - point to test
 * @returns {INSIDE|OUTSIDE|BOUNDARY}
 */
function ray_shoot(polygon, point) {
    let contains = undefined;

    // 1. Quick reject
    // if (polygon.box.not_intersect(point.box)) {
    //     return Flatten.OUTSIDE;
    // }

    let ray = new Flatten.Ray(point);
    let line = new Flatten.Line(ray.pt, ray.norm);

    // 2. Locate relevant edges of the polygon
    const searchBox = new Flatten.Box(
        ray.box.xmin-Flatten.DP_TOL, ray.box.ymin-Flatten.DP_TOL,
        ray.box.xmax+Flatten.DP_TOL, ray.box.ymax+Flatten.DP_TOL
    );

    if (polygon.box.not_intersect(searchBox)) {
        return Flatten.OUTSIDE;
    }

    let resp_edges = polygon.edges.search(searchBox);

    if (resp_edges.length === 0) {
        return Flatten.OUTSIDE;
    }

    // 2.5 Check if boundary
    for (let edge of resp_edges) {
        if (edge.shape.contains(point)) {
            return Flatten.BOUNDARY;
        }
    }

    let faces = [...polygon.faces];

    // 3. Calculate intersections
    let intersections = [];
    for (let edge of resp_edges) {
        for (let ip of ray.intersect(edge.shape)) {

            // If intersection is equal to query point then point lays on boundary
            if (ip.equalTo(point)) {
                return Flatten.BOUNDARY;
            }

            intersections.push({
                pt: ip,
                edge: edge,
                face_index: faces.indexOf(edge.face)
            });
        }
    }

    // 4. Sort intersection in x-ascending order
    intersections.sort((i1, i2) => {
        if (LT(i1.pt.x, i2.pt.x)) {
            return -1;
        }
        if (GT(i1.pt.x, i2.pt.x)) {
            return 1;
        }
        if (i1.face_index < i2.face_index) {
            return -1
        }
        if (i1.face_index > i2.face_index) {
            return 1
        }
        if (i1.edge.arc_length < i2.edge.arc_length) {
            return -1
        }
        if (i1.edge.arc_length > i2.edge.arc_length) {
            return 1
        }
        return 0;
    });

    // 5. Count real intersections, exclude touching
    let counter = 0;

    for (let i = 0; i < intersections.length; i++) {
        let intersection = intersections[i];

        if (intersection.pt.equalTo(intersection.edge.shape.start)) {
            /* skip same point between same edges if already counted */
            if (i > 0 && intersection.pt.equalTo(intersections[i - 1].pt) &&
                intersection.face_index === intersections[i - 1].face_index &&
                intersection.edge.prev === intersections[i - 1].edge) {
                continue;
            }

            let prev_edge = intersection.edge.prev;
            while (EQ_0(prev_edge.length)) {
                prev_edge = prev_edge.prev;
            }
            let prev_tangent = prev_edge.shape.tangentInEnd();
            let prev_point = intersection.pt.translate(prev_tangent);

            let cur_tangent = intersection.edge.shape.tangentInStart();
            let cur_point = intersection.pt.translate(cur_tangent);

            let prev_on_the_left = prev_point.leftTo(line);
            let cur_on_the_left = cur_point.leftTo(line);

            if ((prev_on_the_left && !cur_on_the_left) || (!prev_on_the_left && cur_on_the_left)) {
                counter++;
            }
        } else if (intersection.pt.equalTo(intersection.edge.shape.end)) {
            /* skip same point between same edges if already counted */
            if (i > 0 && intersection.pt.equalTo(intersections[i - 1].pt) &&
                intersection.face_index === intersections[i-1].face_index &&
                intersection.edge.next === intersections[i - 1].edge) {
                continue;
            }

            let next_edge = intersection.edge.next;
            while (EQ_0(next_edge.length)) {
                next_edge = next_edge.next;
            }
            let next_tangent = next_edge.shape.tangentInStart();
            let next_point = intersection.pt.translate(next_tangent);

            let cur_tangent = intersection.edge.shape.tangentInEnd();
            let cur_point = intersection.pt.translate(cur_tangent);

            let next_on_the_left = next_point.leftTo(line);
            let cur_on_the_left = cur_point.leftTo(line);

            if ((next_on_the_left && !cur_on_the_left) || (!next_on_the_left && cur_on_the_left)) {
                counter++;
            }
        } else {        /* intersection point is not a vertex */
            if (intersection.edge.shape instanceof Flatten.Segment) {
                counter++;
            } else {
                /* Check if ray does not touch the curve in the extremal (top or bottom) point */
                let box = intersection.edge.shape.box;
                if (!(EQ(intersection.pt.y, box.ymin) ||
                    EQ(intersection.pt.y, box.ymax))) {
                    counter++;
                }
            }
        }
    }

    // 6. Odd or even?
    contains = counter % 2 === 1 ? INSIDE$2 : OUTSIDE$1;
    return contains;
}

/*
    Calculate relationship between two shapes and return result in the form of
    Dimensionally Extended nine-Intersection Matrix (https://en.wikipedia.org/wiki/DE-9IM)
 */


/**
 * Returns true if shapes are topologically equal:  their interiors intersect and
 * no part of the interior or boundary of one geometry intersects the exterior of the other
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function equal(shape1, shape2) {
    return relate(shape1, shape2).equal();
}

/**
 * Returns true if shapes have at least one point in common, same as "not disjoint"
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function intersect(shape1, shape2) {
    return relate(shape1, shape2).intersect();
}

/**
 * Returns true if shapes have at least one point in common, but their interiors do not intersect
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function touch(shape1, shape2) {
    return relate(shape1, shape2).touch();
}

/**
 * Returns true if shapes have no points in common neither in interior nor in boundary
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function disjoint(shape1, shape2) {
    return !intersect(shape1, shape2);
}

/**
 * Returns true shape1 lies in the interior of shape2
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function inside(shape1, shape2) {
    return relate(shape1, shape2).inside();
}

/**
 * Returns true if every point in shape1 lies in the interior or on the boundary of shape2
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function covered(shape1, shape2) {
    return  relate(shape1, shape2).covered();
}

/**
 * Returns true shape1's interior contains shape2 <br/>
 * Same as inside(shape2, shape1)
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function contain(shape1, shape2) {
    return inside(shape2, shape1);
}

/**
 * Returns true shape1's cover shape2, same as shape2 covered by shape1
 * @param shape1
 * @param shape2
 * @returns {boolean}
 */
function cover(shape1, shape2) {
    return covered(shape2, shape1);
}

/**
 * Returns relation between two shapes as intersection 3x3 matrix, where each
 * element contains relevant intersection as array of shapes.
 * If there is no intersection, element contains empty array
 * If intersection is irrelevant it left undefined. (For example, intersection
 * between two exteriors is usually irrelevant)
 * @param shape1
 * @param shape2
 * @returns {DE9IM}
 */
function relate(shape1, shape2) {
    if (shape1 instanceof Flatten.Line && shape2 instanceof Flatten.Line) {
        return relateLine2Line(shape1,  shape2);
    }
    else if (shape1 instanceof Flatten.Line && shape2 instanceof Flatten.Circle) {
        return relateLine2Circle(shape1, shape2);
    }
    else if (shape1 instanceof Flatten.Line && shape2 instanceof Flatten.Box) {
        return relateLine2Box(shape1, shape2);
    }
    else if ( shape1 instanceof Flatten.Line  && shape2 instanceof Flatten.Polygon) {
        return relateLine2Polygon(shape1, shape2);
    }
    else if ( (shape1 instanceof Flatten.Segment || shape1 instanceof Flatten.Arc)  && shape2 instanceof Flatten.Polygon) {
        return relateShape2Polygon(shape1, shape2);
    }
    else if ( (shape1 instanceof Flatten.Segment || shape1 instanceof Flatten.Arc)  &&
        (shape2 instanceof Flatten.Circle || shape2 instanceof Flatten.Box) ) {
        return relateShape2Polygon(shape1, new Flatten.Polygon(shape2));
    }
    else if (shape1 instanceof Flatten.Polygon && shape2 instanceof Flatten.Polygon) {
        return relatePolygon2Polygon(shape1, shape2);
    }
    else if ((shape1 instanceof Flatten.Circle || shape1 instanceof Flatten.Box) &&
        (shape2 instanceof  Flatten.Circle || shape2 instanceof Flatten.Box)) {
        return relatePolygon2Polygon(new Flatten.Polygon(shape1), new Flatten.Polygon(shape2));
    }
    else if ((shape1 instanceof Flatten.Circle || shape1 instanceof Flatten.Box) && shape2 instanceof Flatten.Polygon) {
        return relatePolygon2Polygon(new Flatten.Polygon(shape1), shape2);
    }
    else if (shape1 instanceof Flatten.Polygon && (shape2 instanceof Flatten.Circle || shape2 instanceof Flatten.Box)) {
        return relatePolygon2Polygon(shape1, new Flatten.Polygon(shape2));
    }
}

function relateLine2Line(line1, line2) {
    let denim = new DE9IM();
    let ip = intersectLine2Line(line1, line2);
    if (ip.length === 0) {       // parallel or equal ?
        if (line1.contains(line2.pt) && line2.contains(line1.pt)) {
            denim.I2I = [line1];   // equal  'T.F...F..'  - no boundary
            denim.I2E = [];
            denim.E2I = [];
        }
        else {                     // parallel - disjoint 'FFTFF*T**'
            denim.I2I = [];
            denim.I2E = [line1];
            denim.E2I = [line2];
        }
    }
    else {                       // intersect   'T********'
        denim.I2I = ip;
        denim.I2E = line1.split(ip);
        denim.E2I = line2.split(ip);
    }
    return denim;
}

function relateLine2Circle(line,circle) {
    let denim = new DE9IM();
    let ip = intersectLine2Circle(line, circle);
    if (ip.length === 0) {
        denim.I2I = [];
        denim.I2B = [];
        denim.I2E = [line];
        denim.E2I = [circle];
    }
    else if (ip.length === 1) {
        denim.I2I = [];
        denim.I2B = ip;
        denim.I2E = line.split(ip);

        denim.E2I = [circle];
    }
    else {       // ip.length == 2
        let multiline = new Multiline$1([line]);
        let ip_sorted = line.sortPoints(ip);
        multiline.split(ip_sorted);
        let splitShapes = multiline.toShapes();

        denim.I2I = [splitShapes[1]];
        denim.I2B = ip_sorted;
        denim.I2E = [splitShapes[0], splitShapes[2]];

        denim.E2I = new Flatten.Polygon([circle.toArc()]).cutWithLine(line);
    }

    return denim;
}

function relateLine2Box(line, box) {
    let denim = new DE9IM();
    let ip = intersectLine2Box(line, box);
    if (ip.length === 0) {
        denim.I2I = [];
        denim.I2B = [];
        denim.I2E = [line];

        denim.E2I = [box];
    }
    else if (ip.length === 1) {
        denim.I2I = [];
        denim.I2B = ip;
        denim.I2E = line.split(ip);

        denim.E2I = [box];
    }
    else {                     // ip.length == 2
        let multiline = new Multiline$1([line]);
        let ip_sorted = line.sortPoints(ip);
        multiline.split(ip_sorted);
        let splitShapes = multiline.toShapes();

        /* Are two intersection points on the same segment of the box boundary ? */
        if (box.toSegments().some( segment => segment.contains(ip[0]) && segment.contains(ip[1]) )) {
            denim.I2I = [];                         // case of touching
            denim.I2B = [splitShapes[1]];
            denim.I2E = [splitShapes[0], splitShapes[2]];

            denim.E2I = [box];
        }
        else {                                       // case of intersection
            denim.I2I = [splitShapes[1]];            // [segment(ip[0], ip[1])];
            denim.I2B = ip_sorted;
            denim.I2E = [splitShapes[0], splitShapes[2]];

            denim.E2I = new Flatten.Polygon(box.toSegments()).cutWithLine(line);
        }
    }
    return denim;
}

function relateLine2Polygon(line, polygon) {
    let denim = new DE9IM();
    let ip = intersectLine2Polygon(line, polygon);
    let multiline = new Multiline$1([line]);
    let ip_sorted = ip.length > 0 ? ip.slice() : line.sortPoints(ip);

    multiline.split(ip_sorted);

    [...multiline].forEach(edge => edge.setInclusion(polygon));

    denim.I2I = [...multiline].filter(edge => edge.bv === Flatten.INSIDE).map(edge => edge.shape);
    denim.I2B = [...multiline].slice(1).map( (edge) => edge.bv === Flatten.BOUNDARY ? edge.shape : edge.shape.start );
    denim.I2E = [...multiline].filter(edge => edge.bv === Flatten.OUTSIDE).map(edge => edge.shape);

    denim.E2I = polygon.cutWithLine(line);

    return denim;
}

function relateShape2Polygon(shape, polygon) {
    let denim = new DE9IM();
    let ip = intersectShape2Polygon(shape, polygon);
    let ip_sorted = ip.length > 0 ? ip.slice() : shape.sortPoints(ip);

    let multiline = new Multiline$1([shape]);
    multiline.split(ip_sorted);

    [...multiline].forEach(edge => edge.setInclusion(polygon));

    denim.I2I = [...multiline].filter(edge => edge.bv === Flatten.INSIDE).map(edge => edge.shape);
    denim.I2B = [...multiline].slice(1).map( (edge) => edge.bv === Flatten.BOUNDARY ? edge.shape : edge.shape.start );
    denim.I2E = [...multiline].filter(edge => edge.bv === Flatten.OUTSIDE).map(edge => edge.shape);


    denim.B2I = [];
    denim.B2B = [];
    denim.B2E = [];
    for (let pt of [shape.start, shape.end]) {
        switch (ray_shoot(polygon, pt)) {
            case Flatten.INSIDE:
                denim.B2I.push(pt);
                break;
            case Flatten.BOUNDARY:
                denim.B2B.push(pt);
                break;
            case Flatten.OUTSIDE:
                denim.B2E.push(pt);
                break;
        }
    }

    // denim.E2I  TODO: calculate, not clear what is expected result

    return denim;
}

function relatePolygon2Polygon(polygon1, polygon2) {
    let denim = new DE9IM();

    let [ip_sorted1, ip_sorted2] = calculateIntersections(polygon1, polygon2);
    let boolean_intersection = intersect$1(polygon1, polygon2);
    let boolean_difference1 = subtract(polygon1, polygon2);
    let boolean_difference2 = subtract(polygon2, polygon1);
    let [inner_clip_shapes1, inner_clip_shapes2] = innerClip(polygon1, polygon2);
    let outer_clip_shapes1 = outerClip(polygon1, polygon2);
    let outer_clip_shapes2 = outerClip(polygon2, polygon1);

    denim.I2I = boolean_intersection.isEmpty() ? [] : [boolean_intersection];
    denim.I2B = inner_clip_shapes2;
    denim.I2E = boolean_difference1.isEmpty() ? [] : [boolean_difference1];

    denim.B2I = inner_clip_shapes1;
    denim.B2B = ip_sorted1;
    denim.B2E = outer_clip_shapes1;

    denim.E2I = boolean_difference2.isEmpty() ? [] : [boolean_difference2];
    denim.E2B = outer_clip_shapes2;
    // denim.E2E    not relevant meanwhile

    return denim;
}

var Relations = /*#__PURE__*/Object.freeze({
    __proto__: null,
    contain: contain,
    cover: cover,
    covered: covered,
    disjoint: disjoint,
    equal: equal,
    inside: inside,
    intersect: intersect,
    relate: relate,
    touch: touch
});

/**
 * Class representing an affine transformation 3x3 matrix:
 * <pre>
 *      [ a  c  tx
 * A =    b  d  ty
 *        0  0  1  ]
 * </pre
 * @type {Matrix}
 */
class Matrix {
    /**
     * Construct new instance of affine transformation matrix <br/>
     * If parameters omitted, construct identity matrix a = 1, d = 1
     * @param {number} a - position(0,0)   sx*cos(alpha)
     * @param {number} c - position (1,0)  -sy*sin(alpha)
     * @param {number} b - position (0,1)  sx*sin(alpha)
     * @param {number} d - position (1,1)  sy*cos(alpha)
     * @param {number} tx - position (2,0) translation by x
     * @param {number} ty - position (2,1) translation by y
     */
    constructor(a = 1, b = 0, c = 0, d = 1, tx = 0, ty = 0) {
        this.a = a;
        this.b = b;
        this.c = c;
        this.d = d;
        this.tx = tx;
        this.ty = ty;
    }

    /**
     * Return new matrix from 3x3 affine transformation matrix
     * @param {AffineMatrix3x3} matrix3x3
     * @returns {Matrix}
     */
    fromMatrix3x3(matrix3x3) {
        const [a, c, tx] = matrix3x3[0];
        const [b, d, ty] = matrix3x3[1];
        return new Matrix(a, b, c, d, tx, ty)
    }

    /**
     * Return 3x3 affine transformation matrix
     * @returns {AffineMatrix3x3}
     */
    toMatrix3x3() {
        return [
            [this.a, this.c, this.tx],
            [this.b, this.d, this.ty],
            [0, 0, 1]
        ]
    }

    /**
     * Return new cloned instance of matrix
     * @return {Matrix}
     **/
    clone() {
        return new Matrix(this.a, this.b, this.c, this.d, this.tx, this.ty);
    };

    /**
     * Transform vector [x,y] using transformation matrix. <br/>
     * Vector [x,y] is an abstract array[2] of numbers and not a FlattenJS object <br/>
     * The result is also an abstract vector [x',y'] = A * [x,y]:
     * <code>
     * [x'       [ ax + cy + tx
     *  y'   =     bx + dy + ty
     *  1]                    1 ]
     * </code>
     * @param {number[]} vector - array[2] of numbers
     * @returns {number[]} transformation result - array[2] of numbers
     */
    transform(vector) {
        return [
            vector[0] * this.a + vector[1] * this.c + this.tx,
            vector[0] * this.b + vector[1] * this.d + this.ty
        ]
    };

    /**
     * Returns result of multiplication of this matrix by other matrix
     * @param {Matrix} other_matrix - matrix to multiply by
     * @returns {Matrix}
     */
    multiply(other_matrix) {
        return new Matrix(
            this.a * other_matrix.a + this.c * other_matrix.b,
            this.b * other_matrix.a + this.d * other_matrix.b,
            this.a * other_matrix.c + this.c * other_matrix.d,
            this.b * other_matrix.c + this.d * other_matrix.d,
            this.a * other_matrix.tx + this.c * other_matrix.ty + this.tx,
            this.b * other_matrix.tx + this.d * other_matrix.ty + this.ty
        )
    };

    /**
     * Return new matrix as a result of multiplication of the current matrix
     * by the matrix(1,0,0,1,tx,ty)
     * @param {Vector} vector - Translation by vector or
     * @param {number} tx - translation by x-axis
     * @param {number} ty - translation by y-axis
     * @returns {Matrix}
     */
    translate(...args) {
        let tx, ty;
        if (args.length == 1 &&  !isNaN(args[0].x) && !isNaN(args[0].y)) {
            tx = args[0].x;
            ty = args[0].y;
        } else if (args.length === 2 && typeof (args[0]) == "number" && typeof (args[1]) == "number") {
            tx = args[0];
            ty = args[1];
        } else {
            throw Errors.ILLEGAL_PARAMETERS;
        }
        return this.multiply(new Matrix(1, 0, 0, 1, tx, ty))
    };

    /**
     * Return new matrix as a result of multiplication of the current matrix
     * by the matrix that defines rotation by given angle (in radians) around
     * center of rotation (centerX,centerY) in counterclockwise direction
     * @param {number} angle - angle in radians
     * @param {number} centerX - center of rotation
     * @param {number} centerY - center of rotation
     * @returns {Matrix}
     */
    rotate(angle, centerX = 0.0, centerY = 0.0) {
        let cos = Math.cos(angle);
        let sin = Math.sin(angle);
        return this
            .translate(centerX, centerY)
            .multiply(new Matrix(cos, sin, -sin, cos, 0, 0))
            .translate(-centerX, -centerY);
    };

    /**
     * Return new matrix as a result of multiplication of the current matrix
     * by the matrix (sx,0,0,sy,0,0) that defines scaling
     * @param {number} sx
     * @param {number} sy
     * @returns {Matrix}
     */
    scale(sx, sy) {
        return this.multiply(new Matrix(sx, 0, 0, sy, 0, 0));
    };

    /**
     * Returns true if two matrix are equal parameter by parameter
     * @param {Matrix} matrix - other matrix
     * @returns {boolean} true if equal, false otherwise
     */
    equalTo(matrix) {
        if (!Flatten.Utils.EQ(this.tx, matrix.tx)) return false;
        if (!Flatten.Utils.EQ(this.ty, matrix.ty)) return false;
        if (!Flatten.Utils.EQ(this.a, matrix.a)) return false;
        if (!Flatten.Utils.EQ(this.b, matrix.b)) return false;
        if (!Flatten.Utils.EQ(this.c, matrix.c)) return false;
        if (!Flatten.Utils.EQ(this.d, matrix.d)) return false;
        return true;
    };
}
Flatten.Matrix = Matrix;
/**
 * Function to create matrix equivalent to "new" constructor
 * @param args
 */
const matrix = (...args) => new Flatten.Matrix(...args);
Flatten.matrix = matrix;

/**
 * Created by Alex Bol on 4/1/2017.
 */
// Abstract base for intervals. Concrete variants extend this.
class IntervalBase {
    constructor(low, high) {
        this.low = low;
        this.high = high;
    }
    get max() {
        return this.clone();
    }
    // Default numeric/date comparison (lexicographic by low then high)
    less_than(other_interval) {
        return this.low < other_interval.low ||
            (this.low === other_interval.low && this.high < other_interval.high);
    }
    equal_to(other_interval) {
        return this.low === other_interval.low && this.high === other_interval.high;
    }
    intersect(other_interval) {
        return !this.not_intersect(other_interval);
    }
    not_intersect(other_interval) {
        return (this.high < other_interval.low || other_interval.high < this.low);
    }
    merge(other_interval) {
        // By default choose min low, max high using < and >
        const low = (this.low === undefined)
            ? other_interval.low
            : ((this.low < other_interval.low) ? this.low : other_interval.low);
        const high = (this.high === undefined)
            ? other_interval.high
            : ((this.high > other_interval.high) ? this.high : other_interval.high);
        // Return instance of the same concrete class
        const cloned = this.clone();
        cloned.low = low;
        cloned.high = high;
        return cloned;
    }
    output() {
        return [this.low, this.high];
    }
    // Instance-level comparator so child classes can customize value comparison semantics
    comparable_less_than(val1, val2) {
        return val1 < val2;
    }
}
// 1D numeric/date interval (default)
class Interval extends IntervalBase {
    clone() {
        return new Interval(this.low, this.high);
    }
}

/**
 * Created by Alex Bol on 3/28/2017.
 */
// module.exports = {
//     RB_TREE_COLOR_RED: 0,
//     RB_TREE_COLOR_BLACK: 1
// };
/**
 * Red-Black Tree color constants
 */
const RB_TREE_COLOR_RED = 1;
const RB_TREE_COLOR_BLACK = 0;

/**
 * Created by Alex Bol on 4/1/2017.
 */
class Node {
    constructor(key, value, left = null, right = null, parent = null, color = RB_TREE_COLOR_BLACK) {
        this.left = left;
        this.right = right;
        this.parent = parent;
        this.color = color;
        this.item = { key: undefined, values: [] };
        if (value !== undefined) {
            this.item.values.push(value);
        }
        // Initialize key if provided
        if (key !== undefined) {
            if (Array.isArray(key)) {
                const [rawLow, rawHigh] = key;
                if (!Number.isNaN(rawLow) && !Number.isNaN(rawHigh)) {
                    let low = rawLow;
                    let high = rawHigh;
                    if (low > high)
                        [low, high] = [high, low];
                    this.item.key = new Interval(low, high);
                }
            }
            else {
                // Assume a concrete IntervalBase implementation was passed
                this.item.key = key;
            }
        }
        this.max = this.item.key ? this.item.key.max : undefined;
    }
    isNil() {
        return (this.item.key === undefined &&
            this.item.values.length === 0 &&
            this.left === null &&
            this.right === null &&
            this.color === RB_TREE_COLOR_BLACK);
    }
    requireKey() {
        if (!this.item.key) {
            throw new Error('Node key is undefined (nil/sentinel). Operation is not applicable.');
        }
        return this.item.key;
    }
    less_than(other_node) {
        // Compare nodes by key only; values are stored in a bucket
        const a = this.requireKey();
        const b = other_node.requireKey();
        return a.less_than(b);
    }
    _value_equal(other_node) {
        // Deprecated in bucket mode; kept for backward compatibility if ever used
        // Compare first elements if exist
        const a = this.item.values[0];
        const b = other_node.item.values[0];
        return a && b && a.equal_to ? a.equal_to(b) : a === b;
    }
    equal_to(other_node) {
        // Nodes are equal if keys are equal; values are kept in a bucket
        const a = this.requireKey();
        const b = other_node.requireKey();
        return a.equal_to(b);
    }
    intersect(other_node) {
        const a = this.requireKey();
        const b = other_node.requireKey();
        return a.intersect(b);
    }
    copy_data(other_node) {
        this.item.key = other_node.item.key;
        this.item.values = other_node.item.values.slice();
    }
    update_max() {
        // use key (Interval) max property instead of key.high
        this.max = this.item.key ? this.item.key.max : undefined;
        if (this.right && this.right.max) {
            this.max = this.max ? this.max.merge(this.right.max) : this.right.max;
        }
        if (this.left && this.left.max) {
            this.max = this.max ? this.max.merge(this.left.max) : this.left.max;
        }
    }
    // Other_node does not intersect any node of left subtree
    not_intersect_left_subtree(search_node) {
        if (!this.left)
            return true;
        const high = this.left.max ? this.left.max.high : this.left.item.key.high;
        const selfKey = this.requireKey();
        const searchKey = search_node.requireKey();
        return selfKey.comparable_less_than(high, searchKey.low);
    }
    // Other_node does not intersect right subtree
    not_intersect_right_subtree(search_node) {
        if (!this.right)
            return true;
        const low = this.right.max ? this.right.max.low : this.right.item.key.low;
        const selfKey = this.requireKey();
        const searchKey = search_node.requireKey();
        return selfKey.comparable_less_than(searchKey.high, low);
    }
}

/**
 * Created by Alex Bol on 3/31/2017.
 */
/**
 * Implementation of interval binary search tree
 * Interval tree stores items which are couples of {key:interval, value: value}
 * Interval is an object with high and low properties or simply pair [low,high] of numeric values
 */
class IntervalTree {
    /**
     * Construct new empty instance of IntervalTree
     */
    constructor() {
        this.root = null;
        this.nil_node = new Node();
    }
    /**
     * Returns number of items stored in the interval tree
     * @returns {number}
     */
    get size() {
        let count = 0;
        this.tree_walk(this.root, (node) => count += node.item.values.length);
        return count;
    }
    /**
     * Returns array of sorted keys in the ascending order
     * @returns {Array}
     */
    get keys() {
        const res = [];
        this.tree_walk(this.root, (node) => res.push(node.item.key.output()));
        return res;
    }
    /**
     * Return array of values in the ascending keys order
     * @returns {Array}
     */
    get values() {
        const res = [];
        this.tree_walk(this.root, (node) => {
            for (const v of node.item.values)
                res.push(v);
        });
        return res;
    }
    /**
     * Returns array of items (<key,value> pairs) in the ascended keys order
     * @returns {Array}
     */
    get items() {
        const res = [];
        this.tree_walk(this.root, (node) => {
            const keyOut = node.item.key.output();
            for (const v of node.item.values) {
                res.push({ key: keyOut, value: v });
            }
        });
        return res;
    }
    /**
     * Returns true if tree is empty
     * @returns {boolean}
     */
    isEmpty() {
        return this.root == null || this.root === this.nil_node;
    }
    /**
     * Clear tree
     */
    clear() {
        this.root = null;
    }
    /**
     * Insert new item into interval tree
     * @param key - interval object or array of two numbers [low, high]
     * @param value - value representing any object (optional)
     * @returns returns reference to inserted node
     */
    insert(key, value = key) {
        if (key === undefined)
            return;
        // If node with the same key exists, append value to its bucket
        const existing = this.tree_search(this.root, new Node(key));
        if (existing) {
            existing.item.values.push(value);
            return existing;
        }
        const insert_node = new Node(key, value, this.nil_node, this.nil_node, null, RB_TREE_COLOR_RED);
        this.tree_insert(insert_node);
        this.recalc_max(insert_node);
        return insert_node;
    }
    /**
     * Returns true if item {key,value} exist in the tree
     * @param key - interval correspondent to keys stored in the tree
     * @param value - value object to be checked
     * @returns true if item {key, value} exist in the tree, false otherwise
     */
    exist(key, value = key) {
        const node = this.tree_search(this.root, new Node(key));
        if (!node)
            return false;
        // If value is omitted (or equals key by default), treat as key existence
        if (arguments.length < 2 || value === key)
            return true;
        // Check if value exists in the bucket
        return node.item.values.some((v) => (v && v.equal_to ? v.equal_to(value) : v === value));
    }
    /**
     * Remove entry {key, value} from the tree
     * @param key - interval correspondent to keys stored in the tree
     * @param value - value object
     * @returns deleted node or undefined if not found
     */
    remove(key, value = key) {
        const node = this.tree_search(this.root, new Node(key));
        if (!node)
            return undefined;
        // If value omitted, remove entire node
        if (arguments.length < 2) {
            this.tree_delete(node);
            return node;
        }
        // Remove one matching value from bucket
        const idx = node.item.values.findIndex((v) => (v && v.equal_to ? v.equal_to(value) : v === value));
        if (idx >= 0) {
            node.item.values.splice(idx, 1);
            // If bucket is now empty, remove node from tree
            if (node.item.values.length === 0) {
                this.tree_delete(node);
            }
            return node;
        }
        return undefined;
    }
    search(interval, outputMapperFn = (value, key) => value === key ? key.output() : value) {
        const search_node = new Node(interval);
        const resp_nodes = [];
        this.tree_search_interval(this.root, search_node, resp_nodes);
        const res = [];
        for (const node of resp_nodes) {
            for (const v of node.item.values) {
                res.push(outputMapperFn(v, node.item.key));
            }
        }
        return res;
    }
    /**
     * Returns true if intersection between given and any interval stored in the tree found
     * @param interval - search interval or tuple [low, high]
     * @returns {boolean}
     */
    intersect_any(interval) {
        const search_node = new Node(interval);
        return this.tree_find_any_interval(this.root, search_node);
    }
    /**
     * Tree visitor. For each node implement a callback function.
     * Method calls a callback function with two parameters (key, value)
     * @param visitor - function to be called for each tree item
     */
    forEach(visitor) {
        this.tree_walk(this.root, (node) => {
            for (const v of node.item.values)
                visitor(node.item.key, v);
        });
    }
    /**
     * Value Mapper. Walk through every node and map node value to another value
     * @param callback - function to be called for each tree item
     */
    map(callback) {
        const tree = new IntervalTree();
        this.tree_walk(this.root, (node) => {
            for (const v of node.item.values) {
                tree.insert(node.item.key, callback(v, node.item.key));
            }
        });
        return tree;
    }
    *iterate(interval, outputMapperFn = (value, key) => value === key ? key.output() : value) {
        let node = null;
        if (interval) {
            node = this.tree_search_nearest_forward(this.root, new Node(interval));
        }
        else if (this.root) {
            node = this.local_minimum(this.root);
        }
        while (node) {
            for (const v of node.item.values) {
                yield outputMapperFn(v, node.item.key);
            }
            node = this.tree_successor(node);
        }
    }
    /**
     * Recalculate max property upward from given node to root
     * @param node - starting node
     */
    recalc_max(node) {
        let node_current = node;
        while (node_current.parent != null) {
            node_current.parent.update_max();
            node_current = node_current.parent;
        }
    }
    /**
     * Insert node into tree and rebalance
     * @param insert_node - node to insert
     */
    tree_insert(insert_node) {
        let current_node = this.root;
        let parent_node = null;
        if (this.root == null || this.root === this.nil_node) {
            this.root = insert_node;
        }
        else {
            while (current_node !== this.nil_node) {
                parent_node = current_node;
                if (insert_node.less_than(current_node)) {
                    current_node = current_node.left;
                }
                else {
                    current_node = current_node.right;
                }
            }
            insert_node.parent = parent_node;
            if (insert_node.less_than(parent_node)) {
                parent_node.left = insert_node;
            }
            else {
                parent_node.right = insert_node;
            }
        }
        this.insert_fixup(insert_node);
    }
    /**
     * Restore red-black tree properties after insertion
     * @param insert_node - inserted node
     */
    insert_fixup(insert_node) {
        let current_node;
        let uncle_node;
        current_node = insert_node;
        while (current_node !== this.root && current_node.parent.color === RB_TREE_COLOR_RED) {
            if (current_node.parent === current_node.parent.parent.left) {
                uncle_node = current_node.parent.parent.right;
                if (uncle_node.color === RB_TREE_COLOR_RED) {
                    current_node.parent.color = RB_TREE_COLOR_BLACK;
                    uncle_node.color = RB_TREE_COLOR_BLACK;
                    current_node.parent.parent.color = RB_TREE_COLOR_RED;
                    current_node = current_node.parent.parent;
                }
                else {
                    if (current_node === current_node.parent.right) {
                        current_node = current_node.parent;
                        this.rotate_left(current_node);
                    }
                    current_node.parent.color = RB_TREE_COLOR_BLACK;
                    current_node.parent.parent.color = RB_TREE_COLOR_RED;
                    this.rotate_right(current_node.parent.parent);
                }
            }
            else {
                uncle_node = current_node.parent.parent.left;
                if (uncle_node.color === RB_TREE_COLOR_RED) {
                    current_node.parent.color = RB_TREE_COLOR_BLACK;
                    uncle_node.color = RB_TREE_COLOR_BLACK;
                    current_node.parent.parent.color = RB_TREE_COLOR_RED;
                    current_node = current_node.parent.parent;
                }
                else {
                    if (current_node === current_node.parent.left) {
                        current_node = current_node.parent;
                        this.rotate_right(current_node);
                    }
                    current_node.parent.color = RB_TREE_COLOR_BLACK;
                    current_node.parent.parent.color = RB_TREE_COLOR_RED;
                    this.rotate_left(current_node.parent.parent);
                }
            }
        }
        this.root.color = RB_TREE_COLOR_BLACK;
    }
    /**
     * Delete node from tree and rebalance
     * @param delete_node - node to delete
     */
    tree_delete(delete_node) {
        let cut_node;
        let fix_node;
        if (delete_node.left === this.nil_node || delete_node.right === this.nil_node) {
            cut_node = delete_node;
        }
        else {
            cut_node = this.tree_successor(delete_node);
        }
        if (cut_node.left !== this.nil_node) {
            fix_node = cut_node.left;
        }
        else {
            fix_node = cut_node.right;
        }
        fix_node.parent = cut_node.parent;
        if (cut_node === this.root) {
            this.root = fix_node;
        }
        else {
            if (cut_node === cut_node.parent.left) {
                cut_node.parent.left = fix_node;
            }
            else {
                cut_node.parent.right = fix_node;
            }
            cut_node.parent.update_max();
        }
        this.recalc_max(fix_node);
        if (cut_node !== delete_node) {
            delete_node.copy_data(cut_node);
            delete_node.update_max();
            this.recalc_max(delete_node);
        }
        if (cut_node.color === RB_TREE_COLOR_BLACK) {
            this.delete_fixup(fix_node);
        }
    }
    /**
     * Restore red-black tree properties after deletion
     * @param fix_node - node to fix from
     */
    delete_fixup(fix_node) {
        let current_node = fix_node;
        let brother_node;
        while (current_node !== this.root &&
            current_node.parent != null &&
            current_node.color === RB_TREE_COLOR_BLACK) {
            if (current_node === current_node.parent.left) {
                brother_node = current_node.parent.right;
                if (brother_node.color === RB_TREE_COLOR_RED) {
                    brother_node.color = RB_TREE_COLOR_BLACK;
                    current_node.parent.color = RB_TREE_COLOR_RED;
                    this.rotate_left(current_node.parent);
                    brother_node = current_node.parent.right;
                }
                if (brother_node.left.color === RB_TREE_COLOR_BLACK &&
                    brother_node.right.color === RB_TREE_COLOR_BLACK) {
                    brother_node.color = RB_TREE_COLOR_RED;
                    current_node = current_node.parent;
                }
                else {
                    if (brother_node.right.color === RB_TREE_COLOR_BLACK) {
                        brother_node.color = RB_TREE_COLOR_RED;
                        brother_node.left.color = RB_TREE_COLOR_BLACK;
                        this.rotate_right(brother_node);
                        brother_node = current_node.parent.right;
                    }
                    brother_node.color = current_node.parent.color;
                    current_node.parent.color = RB_TREE_COLOR_BLACK;
                    brother_node.right.color = RB_TREE_COLOR_BLACK;
                    this.rotate_left(current_node.parent);
                    current_node = this.root;
                }
            }
            else {
                brother_node = current_node.parent.left;
                if (brother_node.color === RB_TREE_COLOR_RED) {
                    brother_node.color = RB_TREE_COLOR_BLACK;
                    current_node.parent.color = RB_TREE_COLOR_RED;
                    this.rotate_right(current_node.parent);
                    brother_node = current_node.parent.left;
                }
                if (brother_node.left.color === RB_TREE_COLOR_BLACK &&
                    brother_node.right.color === RB_TREE_COLOR_BLACK) {
                    brother_node.color = RB_TREE_COLOR_RED;
                    current_node = current_node.parent;
                }
                else {
                    if (brother_node.left.color === RB_TREE_COLOR_BLACK) {
                        brother_node.color = RB_TREE_COLOR_RED;
                        brother_node.right.color = RB_TREE_COLOR_BLACK;
                        this.rotate_left(brother_node);
                        brother_node = current_node.parent.left;
                    }
                    brother_node.color = current_node.parent.color;
                    current_node.parent.color = RB_TREE_COLOR_BLACK;
                    brother_node.left.color = RB_TREE_COLOR_BLACK;
                    this.rotate_right(current_node.parent);
                    current_node = this.root;
                }
            }
        }
        current_node.color = RB_TREE_COLOR_BLACK;
    }
    /**
     * Search for node with given key and value
     * @param node - starting node
     * @param search_node - node to search for
     * @returns found node or undefined
     */
    tree_search(node, search_node) {
        if (node == null || node === this.nil_node)
            return undefined;
        if (search_node.equal_to(node)) {
            return node;
        }
        if (search_node.less_than(node)) {
            return this.tree_search(node.left, search_node);
        }
        else {
            return this.tree_search(node.right, search_node);
        }
    }
    /**
     * Find nearest forward node from given interval
     * @param node - starting node
     * @param search_node - search interval as node
     * @returns nearest forward node or null
     */
    tree_search_nearest_forward(node, search_node) {
        let best = null;
        let curr = node;
        while (curr && curr !== this.nil_node) {
            if (curr.less_than(search_node)) {
                if (curr.intersect(search_node)) {
                    best = curr;
                    curr = curr.left;
                }
                else {
                    curr = curr.right;
                }
            }
            else {
                if (!best || curr.less_than(best))
                    best = curr;
                curr = curr.left;
            }
        }
        return best || null;
    }
    /**
     * Search all intervals intersecting given interval
     * @param node - starting node
     * @param search_node - search interval as node
     * @param res - result array to collect found nodes
     */
    tree_search_interval(node, search_node, res) {
        if (node != null && node !== this.nil_node) {
            if (node.left !== this.nil_node && !node.not_intersect_left_subtree(search_node)) {
                this.tree_search_interval(node.left, search_node, res);
            }
            if (node.intersect(search_node)) {
                res.push(node);
            }
            if (node.right !== this.nil_node && !node.not_intersect_right_subtree(search_node)) {
                this.tree_search_interval(node.right, search_node, res);
            }
        }
    }
    /**
     * Check if any interval intersects with given interval
     * @param node - starting node
     * @param search_node - search interval as node
     * @returns true if intersection found
     */
    tree_find_any_interval(node, search_node) {
        let found = false;
        if (node != null && node !== this.nil_node) {
            if (node.left !== this.nil_node && !node.not_intersect_left_subtree(search_node)) {
                found = this.tree_find_any_interval(node.left, search_node);
            }
            if (!found) {
                found = node.intersect(search_node);
            }
            if (!found && node.right !== this.nil_node && !node.not_intersect_right_subtree(search_node)) {
                found = this.tree_find_any_interval(node.right, search_node);
            }
        }
        return found;
    }
    /**
     * Find node with minimum key in subtree
     * @param node - root of subtree
     * @returns node with minimum key
     */
    local_minimum(node) {
        let node_min = node;
        while (node_min.left != null && node_min.left !== this.nil_node) {
            node_min = node_min.left;
        }
        return node_min;
    }
    /**
     * Find node with maximum key in subtree
     * @param node - root of subtree
     * @returns node with maximum key
     */
    local_maximum(node) {
        let node_max = node;
        while (node_max.right != null && node_max.right !== this.nil_node) {
            node_max = node_max.right;
        }
        return node_max;
    }
    /**
     * Find successor node (next in sorted order)
     * @param node - current node
     * @returns successor node or null
     */
    tree_successor(node) {
        let node_successor;
        let current_node;
        let parent_node;
        if (node.right !== this.nil_node) {
            node_successor = this.local_minimum(node.right);
        }
        else {
            current_node = node;
            parent_node = node.parent;
            while (parent_node != null && parent_node.right === current_node) {
                current_node = parent_node;
                parent_node = parent_node.parent;
            }
            node_successor = parent_node;
        }
        return node_successor;
    }
    /**
     * Left rotation around node x
     * @param x - node to rotate
     */
    rotate_left(x) {
        const y = x.right;
        x.right = y.left;
        if (y.left !== this.nil_node) {
            y.left.parent = x;
        }
        y.parent = x.parent;
        if (x === this.root) {
            this.root = y;
        }
        else {
            if (x === x.parent.left) {
                x.parent.left = y;
            }
            else {
                x.parent.right = y;
            }
        }
        y.left = x;
        x.parent = y;
        if (x !== null && x !== this.nil_node) {
            x.update_max();
        }
        if (y != null && y !== this.nil_node) {
            y.update_max();
        }
    }
    /**
     * Right rotation around node y
     * @param y - node to rotate
     */
    rotate_right(y) {
        const x = y.left;
        y.left = x.right;
        if (x.right !== this.nil_node) {
            x.right.parent = y;
        }
        x.parent = y.parent;
        if (y === this.root) {
            this.root = x;
        }
        else {
            if (y === y.parent.left) {
                y.parent.left = x;
            }
            else {
                y.parent.right = x;
            }
        }
        x.right = y;
        y.parent = x;
        if (y !== null && y !== this.nil_node) {
            y.update_max();
        }
        if (x != null && x !== this.nil_node) {
            x.update_max();
        }
    }
    /**
     * Performs in-order traversal of the tree
     * Applies action callback to each node in ascending order of keys
     * @param node - starting node for traversal (typically root)
     * @param action - callback function to be executed for each node
     */
    tree_walk(node, action) {
        if (node != null && node !== this.nil_node) {
            this.tree_walk(node.left, action);
            action(node);
            this.tree_walk(node.right, action);
        }
    }
    /**
     * Test red-black tree property: all red nodes have exactly two black child nodes
     * @returns true if property holds
     */
    testRedBlackProperty() {
        let res = true;
        this.tree_walk(this.root, function (node) {
            if (node.color === RB_TREE_COLOR_RED) {
                if (!(node.left.color === RB_TREE_COLOR_BLACK &&
                    node.right.color === RB_TREE_COLOR_BLACK)) {
                    res = false;
                }
            }
        });
        return res;
    }
    /**
     * Test red-black tree property: every path from root to leaf has same black height
     * @param node - starting node
     * @returns black height
     * @throws Error if property is violated
     */
    testBlackHeightProperty(node) {
        let height = 0;
        let heightLeft = 0;
        let heightRight = 0;
        if (node.color === RB_TREE_COLOR_BLACK) {
            height++;
        }
        if (node.left !== this.nil_node) {
            heightLeft = this.testBlackHeightProperty(node.left);
        }
        else {
            heightLeft = 1;
        }
        if (node.right !== this.nil_node) {
            heightRight = this.testBlackHeightProperty(node.right);
        }
        else {
            heightRight = 1;
        }
        if (heightLeft !== heightRight) {
            throw new Error('Red-black height property violated');
        }
        height += heightLeft;
        return height;
    }
}

/**
 * Created by Alex Bol on 3/12/2017.
 */


/**
 * Class representing a planar set - a generic container with ability to keep and retrieve shapes and
 * perform spatial queries. Planar set is an extension of Set container, so it supports
 * Set properties and methods
 */
class PlanarSet extends Set {
    /**
     * Create new instance of PlanarSet
     * @param shapes - array or set of geometric objects to store in planar set
     * Each object should have a <b>box</b> property
     */
    constructor(shapes) {
        super(shapes);
        this.index = new IntervalTree();
        this.forEach(shape => this.index.insert(shape));
    }

    /**
     * Add new shape to planar set and to its spatial index.<br/>
     * If shape already exist, it will not be added again.
     * This happens with no error, it is possible to use <i>size</i> property to check if
     * a shape was actually added.<br/>
     * Method returns planar set object updated and may be chained
     * @param {AnyShape | {Box, AnyShape}} entry - shape to be added, should have valid <i>box</i> property
     * Another option to transfer as an object {key: Box, value: AnyShape}
     * @returns {PlanarSet}
     */
    add(entry) {
        let size = this.size;
        const {key, value} = entry;
        const box = key || entry.box;
        const shape = value || entry;
        super.add(shape);
        // size not changed - item not added, probably trying to add same item twice
        if (this.size > size) {
            this.index.insert(box, shape);
        }
        return this;         // in accordance to Set.add interface
    }

    /**
     * Delete shape from planar set. Returns true if shape was actually deleted, false otherwise
     * @param {AnyShape | {Box, AnyShape}} entry - shape to be deleted
     * @returns {boolean}
     */
    delete(entry) {
        const {key, value} = entry;
        const box = key || entry.box;
        const shape = value || entry;
        let deleted = super.delete(shape);
        if (deleted) {
            this.index.remove(box, shape);
        }
        return deleted;
    }

    /**
     * Clear planar set
     */
    clear() {
        super.clear();
        this.index = new IntervalTree();
    }

    /**
     * 2d range search in planar set.<br/>
     * Returns array of all shapes in planar set which bounding box is intersected with query box
     * @param {Box} box - query box
     * @returns {AnyShape[]}
     */
    search(box) {
        let resp = this.index.search(box);
        return resp;
    }

    /**
     * Point location test. Returns array of shapes which contains given point
     * @param {Point} point - query point
     * @returns {AnyShape[]}
     */
    hit(point) {
        let box = new Flatten.Box(point.x - 1, point.y - 1, point.x + 1, point.y + 1);
        let resp = this.index.search(box);
        return resp.filter((shape) => point.on(shape));
    }

    /**
     * Returns svg string to draw all shapes in planar set
     * @returns {String}
     */
    svg() {
        let svgcontent = [...this].reduce((acc, shape) => acc + shape.svg(), "");
        return svgcontent;
    }
}

Flatten.PlanarSet = PlanarSet;

/**
 * Base class representing shape
 * Implement common methods of affine transformations
 */
class Shape {
    get name() {
        throw(Errors.CANNOT_INVOKE_ABSTRACT_METHOD);
    }

    get box() {
        throw(Errors.CANNOT_INVOKE_ABSTRACT_METHOD);
    }

    clone() {
        throw(Errors.CANNOT_INVOKE_ABSTRACT_METHOD);
    }

    /**
     * Returns new shape translated by given vector.
     * Translation vector may be also defined by a pair of numbers.
     * @param {Vector | (number, number) } args - Translation vector
     * or tuple of numbers
     * @returns {Shape}
     */
    translate(...args) {
        return this.transform(new Matrix().translate(...args))
    }

    /**
     * Returns new shape rotated by given angle around given center point.
     * If center point is omitted, rotates around zero point (0,0).
     * Positive value of angle defines rotation in counterclockwise direction,
     * negative angle defines rotation in clockwise direction
     * @param {number} angle - angle in radians
     * @param {Point} [center=(0,0)] center
     * @returns {Shape}
     */
    rotate(angle, center = new Flatten.Point()) {
        return this.transform(new Matrix().rotate(angle, center.x, center.y));
    }

    /**
     * Return new shape with coordinates multiplied by scaling factor
     * @param {number} sx - x-axis scaling factor
     * @param {number} sy - y-axis scaling factor
     * @returns {Shape}
     */
    scale(sx, sy) {
        return this.transform(new Matrix().scale(sx, sy));
    }

    transform(...args) {
        throw(Errors.CANNOT_INVOKE_ABSTRACT_METHOD);
    }

    /**
     * This method returns an object that defines how data will be
     * serialized when called JSON.stringify() method
     * @returns {Object}
     */
    toJSON() {
        return Object.assign({}, this, {name: this.name});
    }

    svg(attrs = {}) {
        throw(Errors.CANNOT_INVOKE_ABSTRACT_METHOD);
    }
}

/**
 * Created by Alex Bol on 2/18/2017.
 */


/**
 *
 * Class representing a point
 * @type {Point}
 */
let Point$3 = class Point extends Shape {
    /**
     * Point may be constructed by two numbers, or by array of two numbers
     * @param {number} x - x-coordinate (float number)
     * @param {number} y - y-coordinate (float number)
     */
    constructor(...args) {
        super();
        /**
         * x-coordinate (float number)
         * @type {number}
         */
        this.x = 0;
        /**
         * y-coordinate (float number)
         * @type {number}
         */
        this.y = 0;

        if (args.length === 0) {
            return;
        }

        if (args.length === 1 && args[0] instanceof Array && args[0].length === 2) {
            let arr = args[0];
            if (typeof (arr[0]) == "number" && typeof (arr[1]) == "number") {
                this.x = arr[0];
                this.y = arr[1];
                return;
            }
        }

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "point") {
            let {x, y} = args[0];
            this.x = x;
            this.y = y;
            return;
        }

        if (args.length === 2) {
            if (typeof (args[0]) == "number" && typeof (args[1]) == "number") {
                this.x = args[0];
                this.y = args[1];
                return;
            }
        }
        throw Errors.ILLEGAL_PARAMETERS;
    }

    /**
     * Returns bounding box of a point
     * @returns {Box}
     */
    get box() {
        return new Flatten.Box(this.x, this.y, this.x, this.y);
    }

    /**
     * Return new cloned instance of point
     * @returns {Point}
     */
    clone() {
        return new Flatten.Point(this.x, this.y);
    }

    get vertices() {
        return [this.clone()];
    }

    /**
     * Returns true if points are equal up to [Flatten.Utils.DP_TOL]{@link DP_TOL} tolerance
     * @param {Point} pt Query point
     * @returns {boolean}
     */
    equalTo(pt) {
        return Flatten.Utils.EQ(this.x, pt.x) && Flatten.Utils.EQ(this.y, pt.y);
    }

    /**
     * Defines predicate "less than" between points. Returns true if the point is less than query points, false otherwise <br/>
     * By definition point1 < point2 if {point1.y < point2.y || point1.y == point2.y && point1.x < point2.x <br/>
     * Numeric values compared with [Flatten.Utils.DP_TOL]{@link DP_TOL} tolerance
     * @param {Point} pt Query point
     * @returns {boolean}
     */
    lessThan(pt) {
        if (Flatten.Utils.LT(this.y, pt.y))
            return true;
        if (Flatten.Utils.EQ(this.y, pt.y) && Flatten.Utils.LT(this.x, pt.x))
            return true;
        return false;
    }

    /**
     * Return new point transformed by affine transformation matrix
     * @param {Matrix} m - affine transformation matrix (a,b,c,d,tx,ty)
     * @returns {Point}
     */
    transform(m) {
        return new Flatten.Point(m.transform([this.x, this.y]))
    }

    /**
     * Returns projection point on given line
     * @param {Line} line Line this point be projected on
     * @returns {Point}
     */
    projectionOn(line) {
        if (this.equalTo(line.pt))                   // this point equal to line anchor point
            return this.clone();

        let vec = new Flatten.Vector(this, line.pt);
        if (Flatten.Utils.EQ_0(vec.cross(line.norm)))    // vector to point from anchor point collinear to normal vector
            return line.pt.clone();

        let dist = vec.dot(line.norm);             // signed distance
        let proj_vec = line.norm.multiply(dist);
        return this.translate(proj_vec);
    }

    /**
     * Returns true if point belongs to the "left" semi-plane, which means, point belongs to the same semi plane where line normal vector points to
     * Return false if point belongs to the "right" semi-plane or to the line itself
     * @param {Line} line Query line
     * @returns {boolean}
     */
    leftTo(line) {
        let vec = new Flatten.Vector(line.pt, this);
        let onLeftSemiPlane = Flatten.Utils.GT(vec.dot(line.norm), 0);
        return onLeftSemiPlane;
    }

    /**
     * Calculate distance and shortest segment from point to shape and return as array [distance, shortest segment]
     * @param {Shape} shape Shape of the one of supported types Point, Line, Circle, Segment, Arc, Polygon or Planar Set
     * @returns {number} distance from point to shape
     * @returns {Segment} shortest segment between point and shape (started at point, ended at shape)
     */
    distanceTo(shape) {
        if (shape instanceof Point) {
            let dx = shape.x - this.x;
            let dy = shape.y - this.y;
            return [Math.sqrt(dx * dx + dy * dy), new Flatten.Segment(this, shape)];
        }

        if (shape instanceof Flatten.Line) {
            return Flatten.Distance.point2line(this, shape);
        }

        if (shape instanceof Flatten.Circle) {
            return Flatten.Distance.point2circle(this, shape);
        }

        if (shape instanceof Flatten.Segment) {
            return Flatten.Distance.point2segment(this, shape);
        }

        if (shape instanceof Flatten.Arc) {
            return Flatten.Distance.point2arc(this, shape);
        }

        if (shape instanceof Flatten.Polygon) {
            return Flatten.Distance.point2polygon(this, shape);
        }

        if (shape instanceof Flatten.PlanarSet) {
            return Flatten.Distance.shape2planarSet(this, shape);
        }

        if (shape instanceof Flatten.Multiline) {
            return Flatten.Distance.shape2multiline(this, shape);
        }
    }

    /**
     * Returns true if point is on a shape, false otherwise
     * @param {Shape} shape
     * @returns {boolean}
     */
    on(shape) {
        if (shape instanceof Flatten.Point) {
            return this.equalTo(shape);
        }

        if (shape.contains && shape.contains instanceof Function) {
            return shape.contains(this);
        }

        throw Flatten.Errors.UNSUPPORTED_SHAPE_TYPE;
    }

    get name() {
        return "point"
    }

    /**
     * Return string to draw point in svg as circle with radius "r" <br/>
     * Accept any valid attributes of svg elements as svg object
     * Defaults attribues are: <br/>
     * {
     *    r:"3",
     *    stroke:"black",
     *    strokeWidth:"1",
     *    fill:"red"
     * }
     * @param {Object} attrs - Any valid attributes of svg circle element, like "r", "stroke", "strokeWidth", "fill"
     * @returns {String}
     */
    svg(attrs = {}) {
        const r = attrs.r ?? 3;            // default radius - 3
        return `\n<circle cx="${this.x}" cy="${this.y}" r="${r}"
            ${convertToString({fill: "red", ...attrs})} />`;
    }
};

Flatten.Point = Point$3;
/**
 * Function to create point equivalent to "new" constructor
 * @param args
 */
const point = (...args) => new Flatten.Point(...args);
Flatten.point = point;

// export {Point};

/**
 * Created by Alex Bol on 2/19/2017.
 */


/**
 * Class representing a vector
 * @type {Vector}
 */
let Vector$1 = class Vector extends Shape {
    /**
     * Vector may be constructed by two points, or by two float numbers,
     * or by array of two numbers or by segment
     * @param {Point} ps - start point
     * @param {Point} pe - end point
     */
    constructor(...args) {
        super();
        /**
         * x-coordinate of a vector (float number)
         * @type {number}
         */
        this.x = 0;
        /**
         * y-coordinate of a vector (float number)
         * @type {number}
         */
        this.y = 0;

        /* return zero vector */
        if (args.length === 0) {
            return;
        }

        if (args.length === 1 && args[0] instanceof Array && args[0].length === 2) {
            let arr = args[0];
            if (typeof (arr[0]) == "number" && typeof (arr[1]) == "number") {
                this.x = arr[0];
                this.y = arr[1];
                return;
            }
        }

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "vector") {
            let {x, y} = args[0];
            this.x = x;
            this.y = y;
            return;
        }

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "segment") {
            let {start, end} = args[0];
            this.x = end.x - start.x;
            this.y = end.y - start.y;
            return;
        }

        if (args.length === 2) {
            let a1 = args[0];
            let a2 = args[1];

            if (typeof (a1) == "number" && typeof (a2) == "number") {
                this.x = a1;
                this.y = a2;
                return;
            }

            if (a1 instanceof Flatten.Point && a2 instanceof Flatten.Point) {
                this.x = a2.x - a1.x;
                this.y = a2.y - a1.y;
                return;
            }

        }

        throw Errors.ILLEGAL_PARAMETERS;
    }

    /**
     * Method clone returns new instance of Vector
     * @returns {Vector}
     */
    clone() {
        return new Flatten.Vector(this.x, this.y);
    }

    /**
     * Slope of the vector in radians from 0 to 2PI
     * @returns {number}
     */
    get slope() {
        let angle = Math.atan2(this.y, this.x);
        if (angle < 0) angle = 2 * Math.PI + angle;
        return angle;
    }

    /**
     * Length of vector
     * @returns {number}
     */
    get length() {
        return Math.sqrt(this.dot(this));
    }

    /**
     * Returns true if the vector is zero length
     * @returns {boolean}
     */
    isZeroLength() {
        return Flatten.Utils.EQ_0(this.length);
    }

    /**
     * Returns true if vectors are equal up to [DP_TOL]{@link http://localhost:63342/flatten-js/docs/global.html#DP_TOL}
     * tolerance
     * @param {Vector} v
     * @returns {boolean}
     */
    equalTo(v) {
        return Flatten.Utils.EQ(this.x, v.x) && Flatten.Utils.EQ(this.y, v.y);
    }

    /**
     * Returns new vector multiplied by scalar
     * @param {number} scalar
     * @returns {Vector}
     */
    multiply(scalar) {
        return (new Flatten.Vector(scalar * this.x, scalar * this.y));
    }

    /**
     * Returns scalar product (dot product) of two vectors <br/>
     * <code>dot_product = (this * v)</code>
     * @param {Vector} v Other vector
     * @returns {number}
     */
    dot(v) {
        return (this.x * v.x + this.y * v.y);
    }

    /**
     * Returns vector product (cross product) of two vectors <br/>
     * <code>cross_product = (this x v)</code>
     * @param {Vector} v Other vector
     * @returns {number}
     */
    cross(v) {
        return (this.x * v.y - this.y * v.x);
    }

    /**
     * Returns unit vector.<br/>
     * Throw error if given vector has zero length
     * @returns {Vector}
     */
    normalize() {
        if (this.isZeroLength()) {
            throw Errors.ZERO_DIVISION;
        }
        return (new Flatten.Vector(this.x / this.length, this.y / this.length));
    }

    /**
     * Returns new vector rotated by given angle,
     * positive angle defines rotation in counterclockwise direction,
     * negative - in clockwise direction
     * Vector only can be rotated around (0,0) point!
     * @param {number} angle - Angle in radians
     * @param {Point} center - Center of rotation, default is (0,0)
     * @returns {Vector}
     */
    rotate(angle, center = new Flatten.Point()) {
        if (center.x === 0 && center.y === 0) {
            return this.transform(new Matrix().rotate(angle));
        }
        throw(Errors.OPERATION_IS_NOT_SUPPORTED);
    }

    /**
     * Return new vector transformed by affine transformation matrix m
     * @param {Matrix} m - affine transformation matrix (a,b,c,d,tx,ty)
     * @returns {Vector}
     */
    transform(m) {
        return new Flatten.Vector(m.transform([this.x, this.y]))
    }

    /**
     * Returns vector rotated 90 degrees counterclockwise
     * @returns {Vector}
     */
    rotate90CCW() {
        return new Flatten.Vector(-this.y, this.x);
    };

    /**
     * Returns vector rotated 90 degrees clockwise
     * @returns {Vector}
     */
    rotate90CW() {
        return new Flatten.Vector(this.y, -this.x);
    };

    /**
     * Return inverted vector
     * @returns {Vector}
     */
    invert() {
        return new Flatten.Vector(-this.x, -this.y);
    }

    /**
     * Return result of addition of other vector to this vector as a new vector
     * @param {Vector} v Other vector
     * @returns {Vector}
     */
    add(v) {
        return new Flatten.Vector(this.x + v.x, this.y + v.y);
    }

    /**
     * Return result of subtraction of other vector from current vector as a new vector
     * @param {Vector} v Another vector
     * @returns {Vector}
     */
    subtract(v) {
        return new Flatten.Vector(this.x - v.x, this.y - v.y);
    }

    /**
     * Return angle between this vector and other vector. <br/>
     * Angle is measured from 0 to 2*PI in the counterclockwise direction
     * from current vector to  another.
     * @param {Vector} v Another vector
     * @returns {number}
     */
    angleTo(v) {
        let norm1 = this.normalize();
        let norm2 = v.normalize();
        let angle = Math.atan2(norm1.cross(norm2), norm1.dot(norm2));
        if (angle < 0) angle += 2 * Math.PI;
        return angle;
    }

    /**
     * Return vector projection of the current vector on another vector
     * @param {Vector} v Another vector
     * @returns {Vector}
     */
    projectionOn(v) {
        let n = v.normalize();
        let d = this.dot(n);
        return n.multiply(d);
    }

    get name() {
        return "vector"
    }
};

Flatten.Vector = Vector$1;

/**
 * Function to create vector equivalent to "new" constructor
 * @param args
 */
const vector$1 = (...args) => new Flatten.Vector(...args);
Flatten.vector = vector$1;

/**
 * Created by Alex Bol on 3/10/2017.
 */


/**
 * Class representing a segment
 * @type {Segment}
 */
let Segment$1 = class Segment extends Shape {
    /**
     *
     * @param {Point} ps - start point
     * @param {Point} pe - end point
     */
    constructor(...args) {
        super();
        /**
         * Start point
         * @type {Point}
         */
        this.ps = new Flatten.Point();
        /**
         * End Point
         * @type {Point}
         */
        this.pe = new Flatten.Point();

        if (args.length === 0) {
            return;
        }

        if (args.length === 1 && args[0] instanceof Array && args[0].length === 4) {
            let coords = args[0];
            this.ps = new Flatten.Point(coords[0], coords[1]);
            this.pe = new Flatten.Point(coords[2], coords[3]);
            return;
        }

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "segment") {
            let {ps, pe} = args[0];
            this.ps = new Flatten.Point(ps.x, ps.y);
            this.pe = new Flatten.Point(pe.x, pe.y);
            return;
        }

        // second point omitted issue #84
        if (args.length === 1 && args[0] instanceof Flatten.Point) {
            this.ps = args[0].clone();
            return;
        }

        if (args.length === 2 && args[0] instanceof Flatten.Point && args[1] instanceof Flatten.Point) {
            this.ps = args[0].clone();
            this.pe = args[1].clone();
            return;
        }

        if (args.length === 4) {
            this.ps = new Flatten.Point(args[0], args[1]);
            this.pe = new Flatten.Point(args[2], args[3]);
            return;
        }

        throw Errors.ILLEGAL_PARAMETERS;
    }

    /**
     * Return new cloned instance of segment
     * @returns {Segment}
     */
    clone() {
        return new Flatten.Segment(this.start, this.end);
    }

    /**
     * Start point
     * @returns {Point}
     */
    get start() {
        return this.ps;
    }

    /**
     * End point
     * @returns {Point}
     */
    get end() {
        return this.pe;
    }


    /**
     * Returns array of start and end point
     * @returns [Point,Point]
     */
    get vertices() {
        return [this.ps.clone(), this.pe.clone()];
    }

    /**
     * Length of a segment
     * @returns {number}
     */
    get length() {
        return this.start.distanceTo(this.end)[0];
    }

    /**
     * Slope of the line - angle to axe x in radians from 0 to 2PI
     * @returns {number}
     */
    get slope() {
        let vec = new Flatten.Vector(this.start, this.end);
        return vec.slope;
    }

    /**
     * Bounding box
     * @returns {Box}
     */
    get box() {
        return new Flatten.Box(
            Math.min(this.start.x, this.end.x),
            Math.min(this.start.y, this.end.y),
            Math.max(this.start.x, this.end.x),
            Math.max(this.start.y, this.end.y)
        )
    }

    /**
     * Returns true if equals to query segment, false otherwise
     * @param {Segment} seg - query segment
     * @returns {boolean}
     */
    equalTo(seg) {
        return this.ps.equalTo(seg.ps) && this.pe.equalTo(seg.pe);
    }

    /**
     * Returns true if segment contains point
     * @param {Point} pt Query point
     * @returns {boolean}
     */
    contains(pt) {
        return Flatten.Utils.EQ_0(this.distanceToPoint(pt));
    }

    /**
     * Returns array of intersection points between segment and other shape
     * @param {Shape} shape - Shape of the one of supported types <br/>
     * @returns {Point[]}
     */
    intersect(shape) {
        if (shape instanceof Flatten.Point) {
            return this.contains(shape) ? [shape] : [];
        }

        if (shape instanceof Flatten.Line) {
            return intersectSegment2Line(this, shape);
        }

        if (shape instanceof Flatten.Ray) {
            return intersectRay2Segment(shape, this);
        }

        if (shape instanceof Flatten.Segment) {
            return  intersectSegment2Segment(this, shape);
        }

        if (shape instanceof Flatten.Circle) {
            return intersectSegment2Circle(this, shape);
        }

        if (shape instanceof Flatten.Box) {
            return intersectSegment2Box(this, shape);
        }

        if (shape instanceof Flatten.Arc) {
            return intersectSegment2Arc(this, shape);
        }

        if (shape instanceof Flatten.Polygon) {
            return  intersectSegment2Polygon(this, shape);
        }

        if (shape instanceof Flatten.Multiline) {
            return intersectShape2Multiline(this, shape);
        }
    }

    /**
     * Calculate distance and shortest segment from segment to shape and return as array [distance, shortest segment]
     * @param {Shape} shape Shape of the one of supported types Point, Line, Circle, Segment, Arc, Polygon or Planar Set
     * @returns {[number, Segment]} shortest segment between segment and shape (started at segment, ended at shape)
     */
    distanceTo(shape) {
        if (shape instanceof Flatten.Point) {
            let [dist, shortest_segment] = Flatten.Distance.point2segment(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Circle) {
            let [dist, shortest_segment] = Flatten.Distance.segment2circle(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Line) {
            let [dist, shortest_segment] = Flatten.Distance.segment2line(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Segment) {
            let [dist, shortest_segment] = Flatten.Distance.segment2segment(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Arc) {
            let [dist, shortest_segment] = Flatten.Distance.segment2arc(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Polygon) {
            let [dist, shortest_segment] = Flatten.Distance.shape2polygon(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.PlanarSet) {
            let [dist, shortest_segment] = Flatten.Distance.shape2planarSet(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Multiline) {
            return Flatten.Distance.shape2multiline(this, shape);
        }
    }

    /**
     * Returns unit vector in the direction from start to end
     * @returns {Vector}
     */
    tangentInStart() {
        let vec = new Flatten.Vector(this.start, this.end);
        return vec.normalize();
    }

    /**
     * Return unit vector in the direction from end to start
     * @returns {Vector}
     */
    tangentInEnd() {
        let vec = new Flatten.Vector(this.end, this.start);
        return vec.normalize();
    }

    /**
     * Returns new segment with swapped start and end points
     * @returns {Segment}
     */
    reverse() {
        return new Segment(this.end, this.start);
    }

    /**
     * When point belongs to segment, return array of two segments split by given point,
     * if point is inside segment. Returns clone of this segment if query point is incident
     * to start or end point of the segment. Returns empty array if point does not belong to segment
     * @param {Point} pt Query point
     * @returns {Segment[]}
     */
    split(pt) {
        if (this.start.equalTo(pt))
            return [null, this.clone()];

        if (this.end.equalTo(pt))
            return [this.clone(), null];

        return [
            new Flatten.Segment(this.start, pt),
            new Flatten.Segment(pt, this.end)
        ]
    }

    /**
     * Return middle point of the segment
     * @returns {Point}
     */
    middle() {
        return new Flatten.Point((this.start.x + this.end.x) / 2, (this.start.y + this.end.y) / 2);
    }

    /**
     * Get point at given length
     * @param {number} length - The length along the segment
     * @returns {Point}
     */
    pointAtLength(length) {
        if (length > this.length || length < 0) return null;
        if (length == 0) return this.start;
        if (length == this.length) return this.end;
        let factor = length / this.length;
        return new Flatten.Point(
            (this.end.x - this.start.x) * factor + this.start.x,
            (this.end.y - this.start.y) * factor + this.start.y
        );
    }

    distanceToPoint(pt) {
        let [dist, ...rest] = Flatten.Distance.point2segment(pt, this);
        return dist;
    };

    definiteIntegral(ymin = 0.0) {
        let dx = this.end.x - this.start.x;
        let dy1 = this.start.y - ymin;
        let dy2 = this.end.y - ymin;
        return (dx * (dy1 + dy2) / 2);
    }

    /**
     * Return new segment transformed using affine transformation matrix
     * @param {Matrix} matrix - affine transformation matrix
     * @returns {Segment} - transformed segment
     */
    transform(matrix = new Flatten.Matrix()) {
        return new Segment(this.ps.transform(matrix), this.pe.transform(matrix))
    }

    /**
     * Returns true if segment start is equal to segment end up to DP_TOL
     * @returns {boolean}
     */
    isZeroLength() {
        return this.ps.equalTo(this.pe)
    }

    /**
     * Sort given array of points from segment start to end, assuming all points lay on the segment
     * @param {Point[]} - array of points
     * @returns {Point[]} new array sorted
     */
    sortPoints(pts) {
        let line = new Flatten.Line(this.start, this.end);
        return line.sortPoints(pts);
    }

    get name() {
        return "segment"
    }

    /**
     * Return string to draw segment in svg
     * @param {Object} attrs - an object with attributes for svg path element,
     * like "stroke", "strokeWidth" <br/>
     * Defaults are stroke:"black", strokeWidth:"1"
     * @returns {string}
     */
    svg(attrs = {}) {
        return `\n<line x1="${this.start.x}" y1="${this.start.y}" x2="${this.end.x}" y2="${this.end.y}" ${convertToString(attrs)} />`;
    }
};

Flatten.Segment = Segment$1;
/**
 * Shortcut method to create new segment
 */
const segment = (...args) => new Flatten.Segment(...args);
Flatten.segment = segment;

/**
 * Created by Alex Bol on 2/20/2017.
 */

let {vector} = Flatten;

/**
 * Class representing a line
 * @type {Line}
 */
let Line$1 = class Line extends Shape {
    /**
     * Line may be constructed by point and normal vector or by two points that a line passes through
     * @param {Point} pt - point that a line passes through
     * @param {Vector|Point} norm - normal vector to a line or second point a line passes through
     */
    constructor(...args) {
        super();
        /**
         * Point a line passes through
         * @type {Point}
         */
        this.pt = new Flatten.Point();
        /**
         * Normal vector to a line <br/>
         * Vector is normalized (length == 1)<br/>
         * Direction of the vector is chosen to satisfy inequality norm * p >= 0
         * @type {Vector}
         */
        this.norm = new Flatten.Vector(0, 1);

        if (args.length === 0) {
            return;
        }

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "line") {
            let {pt, norm} = args[0];
            this.pt = new Flatten.Point(pt);
            this.norm = new Flatten.Vector(norm);
            return;
        }

        if (args.length === 2) {
            let a1 = args[0];
            let a2 = args[1];

            if (a1 instanceof Flatten.Point && a2 instanceof Flatten.Point) {
                this.pt = a1;
                this.norm = Line.points2norm(a1, a2);
                if (this.norm.dot(vector(this.pt.x,this.pt.y)) >= 0) {
                    this.norm.invert();
                }
                return;
            }

            if (a1 instanceof Flatten.Point && a2 instanceof Flatten.Vector) {
                if (Flatten.Utils.EQ_0(a2.x) && Flatten.Utils.EQ_0(a2.y)) {
                    throw Errors.ILLEGAL_PARAMETERS;
                }
                this.pt = a1.clone();
                this.norm = a2.clone();
                this.norm = this.norm.normalize();
                if (this.norm.dot(vector(this.pt.x,this.pt.y)) >= 0) {
                    this.norm.invert();
                }
                return;
            }

            if (a1 instanceof Flatten.Vector && a2 instanceof Flatten.Point) {
                if (Flatten.Utils.EQ_0(a1.x) && Flatten.Utils.EQ_0(a1.y)) {
                    throw Errors.ILLEGAL_PARAMETERS;
                }
                this.pt = a2.clone();
                this.norm = a1.clone();
                this.norm = this.norm.normalize();
                if (this.norm.dot(vector(this.pt.x,this.pt.y)) >= 0) {
                    this.norm.invert();
                }
                return;
            }
        }

        throw Errors.ILLEGAL_PARAMETERS;
    }

    /**
     * Return new cloned instance of line
     * @returns {Line}
     */
    clone() {
        return new Flatten.Line(this.pt, this.norm);
    }

    /* The following methods need for implementation of Edge interface
    /**
     * Line has no start point
     * @returns {undefined}
     */
    get start() {return undefined;}

    /**
     * Line has no end point
     */
    get end() {return undefined;}

    /**
     * Return positive infinity number as length
     * @returns {number}
     */
    get length() {return Number.POSITIVE_INFINITY;}

    /**
     * Returns infinite box
     * @returns {Box}
     */
    get box() {
        return new Flatten.Box(
            Number.NEGATIVE_INFINITY,
            Number.NEGATIVE_INFINITY,
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY
        )
    }

    /**
     * Middle point is undefined
     * @returns {undefined}
     */
    get middle() {return undefined}

    /**
     * Slope of the line - angle in radians between line and axe x from 0 to 2PI
     * @returns {number} - slope of the line
     */
    get slope() {
        let vec = new Flatten.Vector(this.norm.y, -this.norm.x);
        return vec.slope;
    }

    /**
     * Get coefficients [A,B,C] of a standard line equation in the form Ax + By = C
     * @code [A, B, C] = line.standard
     * @returns {number[]} - array of coefficients
     */
    get standard() {
        let A = this.norm.x;
        let B = this.norm.y;
        let C = this.norm.dot(vector(this.pt.x, this.pt.y));

        return [A, B, C];
    }

    /**
     * Return true if parallel or incident to other line
     * @param {Line} other_line - line to check
     * @returns {boolean}
     */
    parallelTo(other_line) {
        return Flatten.Utils.EQ_0(this.norm.cross(other_line.norm));
    }

    /**
     * Returns true if incident to other line
     * @param {Line} other_line - line to check
     * @returns {boolean}
     */
    incidentTo(other_line) {
        return this.parallelTo(other_line) && this.pt.on(other_line);
    }

    /**
     * Returns true if point belongs to line
     * @param {Point} pt Query point
     * @returns {boolean}
     */
    contains(pt) {
        if (this.pt.equalTo(pt)) {
            return true;
        }
        /* Line contains point if vector to point is orthogonal to the line normal vector */
        let vec = new Flatten.Vector(this.pt, pt);
        return Flatten.Utils.EQ_0(this.norm.dot(vec));
    }

    /**
     * Return coordinate of the point that lies on the line in the transformed
     * coordinate system where center is the projection of the point(0,0) to
     * the line and axe y is collinear to the normal vector. <br/>
     * This method assumes that point lies on the line and does not check it
     * @param {Point} pt - point on a line
     * @returns {number}
     */
    coord(pt) {
        return vector(pt.x, pt.y).cross(this.norm);
    }

    /**
     * Returns array of intersection points
     * @param {Shape} shape - shape to intersect with
     * @returns {Point[]}
     */
    intersect(shape) {
        if (shape instanceof Flatten.Point) {
            return this.contains(shape) ? [shape] : [];
        }

        if (shape instanceof Flatten.Line) {
            return intersectLine2Line(this, shape);
        }

        if (shape instanceof Flatten.Ray) {
            return intersectRay2Line(shape, this);
        }

        if (shape instanceof Flatten.Circle) {
            return intersectLine2Circle(this, shape);
        }

        if (shape instanceof Flatten.Box) {
            return intersectLine2Box(this, shape);
        }

        if (shape instanceof Flatten.Segment) {
            return intersectSegment2Line(shape, this);
        }

        if (shape instanceof Flatten.Arc) {
            return intersectLine2Arc(this, shape);
        }

        if (shape instanceof Flatten.Polygon) {
            return  intersectLine2Polygon(this, shape);
        }

        if (shape instanceof Flatten.Multiline) {
            return intersectShape2Multiline(this, shape);
        }

    }

    /**
     * Calculate distance and shortest segment from line to shape and returns array [distance, shortest_segment]
     * @param {Shape} shape Shape of the one of the types Point, Circle, Segment, Arc, Polygon
     * @returns {[number, Segment]}
     */
    distanceTo(shape) {
        if (shape instanceof Flatten.Point) {
            let [distance, shortest_segment] = Flatten.Distance.point2line(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Circle) {
            let [distance, shortest_segment] = Flatten.Distance.circle2line(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Segment) {
            let [distance, shortest_segment] = Flatten.Distance.segment2line(shape, this);
            return [distance, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Arc) {
            let [distance, shortest_segment] = Flatten.Distance.arc2line(shape, this);
            return [distance, shortest_segment.reverse()];
        }

        if (shape instanceof Flatten.Polygon) {
            let [distance, shortest_segment] = Flatten.Distance.shape2polygon(this, shape);
            return [distance, shortest_segment];
        }
    }

    /**
     * Split line with a point or array of points and return array of shapes
     * Assumed (but not checked) that all points lay on the line
     * @param {Point | Point[]} pt
     * @returns {MultilineShapes}
     */
    split(pt) {
        if (pt instanceof Flatten.Point) {
            return [new Flatten.Ray(pt, this.norm), new Flatten.Ray(pt, this.norm)]
        }
        else {
            let multiline = new Flatten.Multiline([this]);
            let sorted_points = this.sortPoints(pt);
            multiline.split(sorted_points);
            return multiline.toShapes();
        }
    }

    /**
     * Return new line rotated by angle
     * @param {number} angle - angle in radians
     * @param {Point} center - center of rotation
     */
    rotate(angle, center = new Flatten.Point()) {
        return new Flatten.Line(
            this.pt.rotate(angle, center),
            this.norm.rotate(angle)
        )
    }

    /**
     * Return new line transformed by affine transformation matrix
     * @param {Matrix} m - affine transformation matrix (a,b,c,d,tx,ty)
     * @returns {Line}
     */
    transform(m) {
        return new Flatten.Line(
            this.pt.transform(m),
            this.norm.clone()
        )
    }

    /**
     * Sort given array of points that lay on a line with respect to coordinate on a line
     * The method assumes that points lay on the line and does not check this
     * @param {Point[]} pts - array of points
     * @returns {Point[]} new array sorted
     */
    sortPoints(pts) {
        return pts.slice().sort( (pt1, pt2) => {
            if (this.coord(pt1) < this.coord(pt2)) {
                return -1;
            }
            if (this.coord(pt1) > this.coord(pt2)) {
                return 1;
            }
            return 0;
        })
    }

    get name() {
        return "line"
    }

    /**
     * Return string to draw svg segment representing line inside given box
     * @param {Box} box Box representing drawing area
     * @param {Object} attrs - an object with attributes of svg circle element
     */
    svg(box, attrs = {}) {
        let ip = intersectLine2Box(this, box);
        if (ip.length === 0)
            return "";
        let ps = ip[0];
        let pe = ip.length === 2 ? ip[1] : ip.find(pt => !pt.equalTo(ps));
        if (pe === undefined) pe = ps;
        let segment = new Flatten.Segment(ps, pe);
        return segment.svg(attrs);
    }

    static points2norm(pt1, pt2) {
        if (pt1.equalTo(pt2)) {
            throw Errors.ILLEGAL_PARAMETERS;
        }
        let vec = new Flatten.Vector(pt1, pt2);
        let unit = vec.normalize();
        return unit.rotate90CCW();
    }
};

Flatten.Line = Line$1;
/**
 * Function to create line equivalent to "new" constructor
 * @param args
 */
const line = (...args) => new Flatten.Line(...args);
Flatten.line = line;

/**
 * Created by Alex Bol on 3/6/2017.
 */


/**
 * Class representing a circle
 * @type {Circle}
 */
let Circle$1 = class Circle extends Shape {
    /**
     * Class private property
     * @type {string}
     */

    /**
     *
     * @param {Point} pc - circle center point
     * @param {number} r - circle radius
     */
    constructor(...args) {
        super();
        /**
         * Circle center
         * @type {Point}
         */
        this.pc = new Flatten.Point();
        /**
         * Circle radius
         * @type {number}
         */
        this.r = 1;

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "circle") {
            let {pc, r} = args[0];
            this.pc = new Flatten.Point(pc);
            this.r = r;
        } else {
            let [pc, r] = [...args];
            if (pc && pc instanceof Flatten.Point) this.pc = pc.clone();
            if (r !== undefined) this.r = r;
        }
        // throw Errors.ILLEGAL_PARAMETERS;    unreachable code
    }

    /**
     * Return new cloned instance of circle
     * @returns {Circle}
     */
    clone() {
        return new Flatten.Circle(this.pc.clone(), this.r);
    }

    /**
     * Circle center
     * @returns {Point}
     */
    get center() {
        return this.pc;
    }

    /**
     * Circle bounding box
     * @returns {Box}
     */
    get box() {
        return new Flatten.Box(
            this.pc.x - this.r,
            this.pc.y - this.r,
            this.pc.x + this.r,
            this.pc.y + this.r
        );
    }

    /**
     * Return true if circle contains shape: no point of shape lies outside of the circle
     * @param {Shape} shape - test shape
     * @returns {boolean}
     */
    contains(shape) {
        if (shape instanceof Flatten.Point) {
            return Flatten.Utils.LE(shape.distanceTo(this.center)[0], this.r);
        }

        if (shape instanceof Flatten.Segment) {
            return Flatten.Utils.LE(shape.start.distanceTo(this.center)[0], this.r) &&
                Flatten.Utils.LE(shape.end.distanceTo(this.center)[0], this.r);
        }

        if (shape instanceof Flatten.Arc) {
            return this.intersect(shape).length === 0 &&
                Flatten.Utils.LE(shape.start.distanceTo(this.center)[0], this.r) &&
                Flatten.Utils.LE(shape.end.distanceTo(this.center)[0], this.r);
        }

        if (shape instanceof Flatten.Circle) {
            return this.intersect(shape).length === 0 &&
                Flatten.Utils.LE(shape.r, this.r) &&
                Flatten.Utils.LE(shape.center.distanceTo(this.center)[0], this.r);
        }

        /* TODO: box, polygon */
    }

    /**
     * Transform circle to closed arc
     * @param {boolean} counterclockwise
     * @returns {Arc}
     */
    toArc(counterclockwise = true) {
        return new Flatten.Arc(this.center, this.r, Math.PI, -Math.PI, counterclockwise);
    }

    /**
     * Method scale is supported only for uniform scaling of the circle with (0,0) center
     * @param {number} sx
     * @param {number} sy
     * @returns {Circle}
     */
    scale(sx, sy) {
        if (sx !== sy)
            throw Errors.OPERATION_IS_NOT_SUPPORTED
        if (!(this.pc.x === 0.0 && this.pc.y === 0.0))
            throw Errors.OPERATION_IS_NOT_SUPPORTED
        return new Flatten.Circle(this.pc, this.r*sx)
    }

    /**
     * Return new circle transformed using affine transformation matrix
     * @param {Matrix} matrix - affine transformation matrix
     * @returns {Circle}
     */
    transform(matrix = new Flatten.Matrix()) {
        return new Flatten.Circle(this.pc.transform(matrix), this.r)
    }

    /**
     * Returns array of intersection points between circle and other shape
     * @param {Shape} shape Shape of the one of supported types
     * @returns {Point[]}
     */
    intersect(shape) {
        if (shape instanceof Flatten.Point) {
            return this.contains(shape) ? [shape] : [];
        }
        if (shape instanceof Flatten.Line) {
            return intersectLine2Circle(shape, this);
        }
        if (shape instanceof Flatten.Ray) {
            return intersectRay2Circle(shape, this);
        }
        if (shape instanceof Flatten.Segment) {
            return intersectSegment2Circle(shape, this);
        }

        if (shape instanceof Flatten.Circle) {
            return intersectCircle2Circle(shape, this);
        }

        if (shape instanceof Flatten.Box) {
            return intersectCircle2Box(this, shape);
        }

        if (shape instanceof Flatten.Arc) {
            return intersectArc2Circle(shape, this);
        }
        if (shape instanceof Flatten.Polygon) {
            return intersectCircle2Polygon(this, shape);
        }
        if (shape instanceof Flatten.Multiline) {
            return intersectShape2Multiline(this, shape);
        }
    }

    /**
     * Calculate distance and shortest segment from circle to shape and return array [distance, shortest segment]
     * @param {Shape} shape Shape of the one of supported types Point, Line, Circle, Segment, Arc, Polygon or Planar Set
     * @returns {number} distance from circle to shape
     * @returns {Segment} shortest segment between circle and shape (started at circle, ended at shape)

     */
    distanceTo(shape) {
        if (shape instanceof Flatten.Point) {
            let [distance, shortest_segment] = Flatten.Distance.point2circle(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Circle) {
            let [distance, shortest_segment] = Flatten.Distance.circle2circle(this, shape);
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Line) {
            let [distance, shortest_segment] = Flatten.Distance.circle2line(this, shape);
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Segment) {
            let [distance, shortest_segment] = Flatten.Distance.segment2circle(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Arc) {
            let [distance, shortest_segment] = Flatten.Distance.arc2circle(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.Polygon) {
            let [distance, shortest_segment] = Flatten.Distance.shape2polygon(this, shape);
            return [distance, shortest_segment];
        }

        if (shape instanceof Flatten.PlanarSet) {
            let [dist, shortest_segment] = Flatten.Distance.shape2planarSet(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Multiline) {
            let [dist, shortest_segment] = Flatten.Distance.shape2multiline(this, shape);
            return [dist, shortest_segment];
        }
    }

    get name() {
        return "circle"
    }

    /**
     * Return string to draw circle in svg
     * @param {Object} attrs - an object with attributes of svg circle element
     * @returns {string}
     */
    svg(attrs = {}) {
        return `\n<circle cx="${this.pc.x}" cy="${this.pc.y}" r="${this.r}"
                ${convertToString({fill: "none", ...attrs})} />`;
    }

};

Flatten.Circle = Circle$1;
/**
 * Shortcut to create new circle
 * @param args
 */
const circle = (...args) => new Flatten.Circle(...args);
Flatten.circle = circle;

/**
 * Created by Alex Bol on 3/10/2017.
 */


/**
 * Class representing a circular arc
 * @type {Arc}
 */
class Arc extends Shape {
    /**
     *
     * @param {Point} pc - arc center
     * @param {number} r - arc radius
     * @param {number} startAngle - start angle in radians from 0 to 2*PI
     * @param {number} endAngle - end angle in radians from 0 to 2*PI
     * @param {boolean} counterClockwise - arc direction, true - clockwise, false - counterclockwise
     */
    constructor(...args) {
        super();
        /**
         * Arc center
         * @type {Point}
         */
        this.pc = new Flatten.Point();
        /**
         * Arc radius
         * @type {number}
         */
        this.r = 1;
        /**
         * Arc start angle in radians
         * @type {number}
         */
        this.startAngle = 0;
        /**
         * Arc end angle in radians
         * @type {number}
         */
        this.endAngle = 2 * Math.PI;
        /**
         * Arc orientation
         * @type {boolean}
         */
        this.counterClockwise = true;

        if (args.length === 0)
            return;

        if (args.length === 1 && args[0] instanceof Object && args[0].name === "arc") {
            let {pc, r, startAngle, endAngle, counterClockwise} = args[0];
            this.pc = new Flatten.Point(pc.x, pc.y);
            this.r = r;
            this.startAngle = startAngle;
            this.endAngle = endAngle;
            this.counterClockwise = counterClockwise;
        } else {
            let [pc, r, startAngle, endAngle, counterClockwise] = [...args];
            if (pc && pc instanceof Flatten.Point) this.pc = pc.clone();
            if (r !== undefined) this.r = r;
            if (startAngle !== undefined) this.startAngle = startAngle;
            if (endAngle !== undefined) this.endAngle = endAngle;
            if (counterClockwise !== undefined) this.counterClockwise = counterClockwise;
        }

        // throw Flatten.Errors.ILLEGAL_PARAMETERS; unreachable code
    }

    /**
     * Return new cloned instance of arc
     * @returns {Arc}
     */
    clone() {
        return new Flatten.Arc(this.pc.clone(), this.r, this.startAngle, this.endAngle, this.counterClockwise);
    }

    /**
     * Get sweep angle in radians. Sweep angle is non-negative number from 0 to 2*PI
     * @returns {number}
     */
    get sweep() {
        let startAngle = this.startAngle;
        let endAngle = this.endAngle;

        // check full circle
        if (Flatten.Utils.EQ(Math.abs(startAngle - endAngle), Flatten.PIx2)) {
            return Flatten.PIx2;
        }

        // normalize angles
        if (Math.abs(startAngle) > Flatten.PIx2) {
            startAngle -= Math.trunc(startAngle / Flatten.PIx2) * Flatten.PIx2;
        }
        if (startAngle < 0) {
            startAngle += Flatten.PIx2;
        }
        if (Math.abs(endAngle) > Flatten.PIx2) {
            endAngle -= Math.trunc(endAngle / Flatten.PIx2) * Flatten.PIx2;
        }
        if (endAngle < 0) {
            endAngle += Flatten.PIx2;
        }

        // calculate sweep
        let sweep = this.counterClockwise ? endAngle - startAngle : startAngle - endAngle;
        if (sweep < 0) {
            sweep += Flatten.PIx2;
        }
        return sweep
    }

    /**
     * Get start point of arc
     * @returns {Point}
     */
    get start() {
        let p0 = new Flatten.Point(this.pc.x + this.r, this.pc.y);
        return p0.rotate(this.startAngle, this.pc);
    }

    /**
     * Get end point of arc
     * @returns {Point}
     */
    get end() {
        let p0 = new Flatten.Point(this.pc.x + this.r, this.pc.y);
        return p0.rotate(this.endAngle, this.pc);
    }

    /**
     * Get center of arc
     * @returns {Point}
     */
    get center() {
        return this.pc.clone();
    }

    get vertices() {
        return [this.start.clone(), this.end.clone()];
    }

    /**
     * Get arc length
     * @returns {number}
     */
    get length() {
        return Math.abs(this.sweep * this.r);
    }

    /**
     * Get bounding box of the arc
     * @returns {Box}
     */
    get box() {
        let func_arcs = this.breakToFunctional();
        let box = func_arcs.reduce((acc, arc) => acc.merge(arc.start.box), new Flatten.Box());
        box = box.merge(this.end.box);
        return box;
    }

    /**
     * Returns true if arc contains point, false otherwise
     * @param {Point} pt - point to test
     * @returns {boolean}
     */
    contains(pt) {
        // first check if  point on circle (pc,r)
        if (!Flatten.Utils.EQ(this.pc.distanceTo(pt)[0], this.r))
            return false;

        // point on circle

        if (pt.equalTo(this.start))
            return true;

        let angle = new Flatten.Vector(this.pc, pt).slope;
        let test_arc = new Flatten.Arc(this.pc, this.r, this.startAngle, angle, this.counterClockwise);
        return Flatten.Utils.LE(test_arc.length, this.length);
    }

    /**
     * When given point belongs to arc, return array of two arcs split by this point. If points is incident
     * to start or end point of the arc, return clone of the arc. If point does not belong to the arcs, return
     * empty array.
     * @param {Point} pt Query point
     * @returns {Arc[]}
     */
    split(pt) {
        if (this.start.equalTo(pt))
            return [null, this.clone()];

        if (this.end.equalTo(pt))
            return [this.clone(), null];

        let angle = new Flatten.Vector(this.pc, pt).slope;

        return [
            new Flatten.Arc(this.pc, this.r, this.startAngle, angle, this.counterClockwise),
            new Flatten.Arc(this.pc, this.r, angle, this.endAngle, this.counterClockwise)
        ]
    }

    /**
     * Return middle point of the arc
     * @returns {Point}
     */
    middle() {
        let endAngle = this.counterClockwise ? this.startAngle + this.sweep / 2 : this.startAngle - this.sweep / 2;
        let arc = new Flatten.Arc(this.pc, this.r, this.startAngle, endAngle, this.counterClockwise);
        return arc.end;
    }

    /**
     * Get point at given length
     * @param {number} length - The length along the arc
     * @returns {Point}
     */
    pointAtLength(length) {
        if (length > this.length || length < 0) return null;
        if (length === 0) return this.start;
        if (length === this.length) return this.end;
        let factor = length / this.length;
        let endAngle = this.counterClockwise ? this.startAngle + this.sweep * factor : this.startAngle - this.sweep * factor;
        let arc = new Flatten.Arc(this.pc, this.r, this.startAngle, endAngle, this.counterClockwise);
        return arc.end;
    }

    /**
     * Returns chord height ("sagitta") of the arc
     * @returns {number}
     */
    chordHeight() {
        return (1.0 - Math.cos(Math.abs(this.sweep / 2.0))) * this.r;
    }

    /**
     * Returns array of intersection points between arc and other shape
     * @param {Shape} shape Shape of the one of supported types <br/>
     * @returns {Point[]}
     */
    intersect(shape) {
        if (shape instanceof Flatten.Point) {
            return this.contains(shape) ? [shape] : [];
        }
        if (shape instanceof Flatten.Line) {
            return intersectLine2Arc(shape, this);
        }
        if (shape instanceof Flatten.Ray) {
            return intersectRay2Arc(shape, this);
        }
        if (shape instanceof Flatten.Circle) {
            return intersectArc2Circle(this, shape);
        }
        if (shape instanceof Flatten.Segment) {
            return intersectSegment2Arc(shape, this);
        }
        if (shape instanceof Flatten.Box) {
            return intersectArc2Box(this, shape);
        }
        if (shape instanceof Flatten.Arc) {
            return intersectArc2Arc(this, shape);
        }
        if (shape instanceof Flatten.Polygon) {
            return intersectArc2Polygon(this, shape);
        }
        if (shape instanceof Flatten.Multiline) {
            return intersectShape2Multiline(this, shape);
        }
    }

    /**
     * Calculate distance and shortest segment from arc to shape and return array [distance, shortest segment]
     * @param {Shape} shape Shape of the one of supported types Point, Line, Circle, Segment, Arc, Polygon or Planar Set
     * @returns {number} distance from arc to shape
     * @returns {Segment} shortest segment between arc and shape (started at arc, ended at shape)

     */
    distanceTo(shape) {
        if (shape instanceof Flatten.Point) {
            let [dist, shortest_segment] = Flatten.Distance.point2arc(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Circle) {
            let [dist, shortest_segment] = Flatten.Distance.arc2circle(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Line) {
            let [dist, shortest_segment] = Flatten.Distance.arc2line(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Segment) {
            let [dist, shortest_segment] = Flatten.Distance.segment2arc(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Arc) {
            let [dist, shortest_segment] = Flatten.Distance.arc2arc(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Polygon) {
            let [dist, shortest_segment] = Flatten.Distance.shape2polygon(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.PlanarSet) {
            let [dist, shortest_segment] = Flatten.Distance.shape2planarSet(this, shape);
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Multiline) {
           return Flatten.Distance.shape2multiline(this, shape);
        }
    }

    /**
     * Breaks arc in extreme point 0, pi/2, pi, 3*pi/2 and returns array of sub-arcs
     * @returns {Arc[]}
     */
    breakToFunctional() {
        let func_arcs_array = [];
        let angles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
        let startAngle = this.startAngle;
        let endAngle = this.endAngle;

        // check full circle before normalizing angles
        if (Flatten.Utils.EQ(Math.abs(startAngle - endAngle), Flatten.PIx2)) {
            endAngle = startAngle;
        }

        // normalize angles
        if (Math.abs(startAngle) > Flatten.PIx2) {
            startAngle -= Math.trunc(startAngle / Flatten.PIx2) * Flatten.PIx2;
        }
        if (startAngle < 0) {
            startAngle += Flatten.PIx2;
        }
        if (Math.abs(endAngle) > Flatten.PIx2) {
            endAngle -= Math.trunc(endAngle / Flatten.PIx2) * Flatten.PIx2;
        }
        if (endAngle < 0) {
            endAngle += Flatten.PIx2;
        }

        // set up the loop
        let prev = startAngle;
        let next;
        let firstj;
        let d;
        if (this.counterClockwise) {
            firstj = Math.ceil(startAngle / (Math.PI / 2)) % 4;
            d = 1;
        } else {
            firstj = Math.floor(startAngle / (Math.PI / 2)) % 4;
            d = -1;
        }

        // loop over crossings while incremental sweep is less than sweep
        for (let i = 0, j = firstj; i < 4; i++, j = (j + d + 4) % 4) {
            next = angles[j];
            if (next === prev) {
                continue;
            }
            let incrementalSweep = this.counterClockwise ? next - startAngle : startAngle - next;
            if (incrementalSweep < 0) {
                incrementalSweep += Flatten.PIx2;
            }
            if (incrementalSweep > this.sweep) {
                break;
            }
            func_arcs_array.push(new Flatten.Arc(this.pc, this.r, prev, next, this.counterClockwise));
            prev = next;
        }

        // return the original arc for no crossings
        if (func_arcs_array.length === 0) {
            func_arcs_array.push(this);
            return func_arcs_array;
        }

        // add the last arc
        next = endAngle;
        if (prev !== next) {
            func_arcs_array.push(new Flatten.Arc(this.pc, this.r, prev, next, this.counterClockwise));
        }

        return func_arcs_array;
    }

    /**
     * Return tangent unit vector in the start point in the direction from start to end
     * @returns {Vector}
     */
    tangentInStart() {
        let vec = new Flatten.Vector(this.pc, this.start);
        let angle = this.counterClockwise ? Math.PI / 2. : -Math.PI / 2.;
        return vec.rotate(angle).normalize();
    }

    /**
     * Return tangent unit vector in the end point in the direction from end to start
     * @returns {Vector}
     */
    tangentInEnd() {
        let vec = new Flatten.Vector(this.pc, this.end);
        let angle = this.counterClockwise ? -Math.PI / 2. : Math.PI / 2.;
        return vec.rotate(angle).normalize();
    }

    /**
     * Returns new arc with swapped start and end angles and reversed direction
     * @returns {Arc}
     */
    reverse() {
        return new Flatten.Arc(this.pc, this.r, this.endAngle, this.startAngle, !this.counterClockwise);
    }

    /**
     * Return new arc transformed using affine transformation matrix <br/>
     * @param {Matrix} matrix - affine transformation matrix
     * @returns {Arc}
     */
    transform(matrix = new Flatten.Matrix()) {
        let newStart = this.start.transform(matrix);
        let newEnd = this.end.transform(matrix);
        let newCenter = this.pc.transform(matrix);
        let newDirection = this.counterClockwise;
        if (matrix.a * matrix.d < 0) {
          newDirection = !newDirection;
        }
        return Flatten.Arc.arcSE(newCenter, newStart, newEnd, newDirection);
    }

    static arcSE(center, start, end, counterClockwise) {
        let {vector} = Flatten;
        let startAngle = vector(center, start).slope;
        let endAngle = vector(center, end).slope;
        if (Flatten.Utils.EQ(startAngle, endAngle)) {
            endAngle += 2 * Math.PI;
            counterClockwise = true;
        }
        let r = vector(center, start).length;

        return new Flatten.Arc(center, r, startAngle, endAngle, counterClockwise);
    }

    definiteIntegral(ymin = 0) {
        let f_arcs = this.breakToFunctional();
        let area = f_arcs.reduce((acc, arc) => acc + arc.circularSegmentDefiniteIntegral(ymin), 0.0);
        return area;
    }

    circularSegmentDefiniteIntegral(ymin) {
        let segment = new Flatten.Segment(this.start, this.end);
        let areaTrapez = segment.definiteIntegral(ymin);
        // can't be full circle after breakToFunctional, consider zero-arc
        let areaCircularSegment = Flatten.Utils.EQ(this.sweep, Flatten.PIx2)
            ? 0 : this.circularSegmentArea();
        return this.counterClockwise ? areaTrapez - areaCircularSegment : areaTrapez + areaCircularSegment;
    }

    circularSegmentArea() {
        return (0.5 * this.r * this.r * (this.sweep - Math.sin(this.sweep)))
    }

    /**
     * Sort given array of points from arc start to end, assuming all points lay on the arc
     * @param {Point[]} pts array of points
     * @returns {Point[]} new array sorted
     */
    sortPoints(pts) {
        let {vector} = Flatten;
        return pts.slice().sort( (pt1, pt2) => {
            let slope1 = vector(this.pc, pt1).slope;
            let slope2 = vector(this.pc, pt2).slope;
            if (slope1 < slope2) {
                return -1;
            }
            if (slope1 > slope2) {
                return 1;
            }
            return 0;
        })
    }

    get name() {
        return "arc"
    }

    /**
     * Return string to draw arc in svg
     * @param {Object} attrs - an object with attributes of svg path element
     * @returns {string}
     */
    svg(attrs = {}) {
        let largeArcFlag = this.sweep <= Math.PI ? "0" : "1";
        let sweepFlag = this.counterClockwise ? "1" : "0";

        if (Flatten.Utils.EQ(this.sweep, 2 * Math.PI)) {
            let circle = new Flatten.Circle(this.pc, this.r);
            return circle.svg(attrs);
        } else {
            return `\n<path d="M${this.start.x},${this.start.y}
                             A${this.r},${this.r} 0 ${largeArcFlag},${sweepFlag} ${this.end.x},${this.end.y}"
                    ${convertToString({fill: "none", ...attrs})} />`
        }
    }

}

Flatten.Arc = Arc;
/**
 * Function to create arc equivalent to "new" constructor
 * @param args
 */
const arc = (...args) => new Flatten.Arc(...args);
Flatten.arc = arc;

/**
 * Created by Alex Bol on 3/7/2017.
 */

/**
 * Class Box represents bounding box of the shape.
 * It may also represent axis-aligned rectangle
 * @type {Box}
 */
class Box extends Shape {
    /**
     *
     * @param {number} xmin - minimal x coordinate
     * @param {number} ymin - minimal y coordinate
     * @param {number} xmax - maximal x coordinate
     * @param {number} ymax - maximal y coordinate
     */
    constructor(xmin = undefined, ymin = undefined, xmax = undefined, ymax = undefined) {
        super();
        /**
         * Minimal x coordinate
         * @type {number}
         */
        this.xmin = xmin;
        /**
         * Minimal y coordinate
         * @type {number}
         */
        this.ymin = ymin;
        /**
         * Maximal x coordinate
         * @type {number}
         */
        this.xmax = xmax;
        /**
         * Maximal y coordinate
         * @type {number}
         */
        this.ymax = ymax;
    }

    /**
     * Return new cloned instance of box
     * @returns {Box}
     */
    clone() {
        return new Box(this.xmin, this.ymin, this.xmax, this.ymax);
    }

    /**
     * Property low need for interval tree interface
     * @returns {Point}
     */
    get low() {
        return new Flatten.Point(this.xmin, this.ymin);
    }

    /**
     * Property high need for interval tree interface
     * @returns {Point}
     */
    get high() {
        return new Flatten.Point(this.xmax, this.ymax);
    }

    /**
     * Property max returns the box itself !
     * @returns {Box}
     */
    get max() {
        return this.clone();
    }
    
    /**
     * Return center of the box
     * @returns {Point}
     */
    get center() {
        return new Flatten.Point((this.xmin + this.xmax) / 2, (this.ymin + this.ymax) / 2);
    }

    /**
     * Return the width of the box
     * @returns {number}
     */
    get width() {
        return Math.abs(this.xmax - this.xmin);
    }

    /**
     * Return the height of the box
     * @returns {number}
     */
    get height() {
        return Math.abs(this.ymax - this.ymin);
    }
    
    /**
     * Return property box like all other shapes
     * @returns {Box}
     */
    get box() {
        return this.clone();
    }

    /**
     * Returns true if not intersected with other box
     * @param {Box} other_box - other box to test
     * @returns {boolean}
     */
    not_intersect(other_box) {
        return (
            this.xmax < other_box.xmin ||
            this.xmin > other_box.xmax ||
            this.ymax < other_box.ymin ||
            this.ymin > other_box.ymax
        );
    }

    /**
     * Returns true if intersected with other box
     * @param {Box} other_box - Query box
     * @returns {boolean}
     */
    intersect(other_box) {
        return !this.not_intersect(other_box);
    }

    /**
     * Returns new box merged with other box
     * @param {Box} other_box - Other box to merge with
     * @returns {Box}
     */
    merge(other_box) {
        return new Box(
            this.xmin === undefined ? other_box.xmin : Math.min(this.xmin, other_box.xmin),
            this.ymin === undefined ? other_box.ymin : Math.min(this.ymin, other_box.ymin),
            this.xmax === undefined ? other_box.xmax : Math.max(this.xmax, other_box.xmax),
            this.ymax === undefined ? other_box.ymax : Math.max(this.ymax, other_box.ymax)
        );
    }

    /**
     * Defines predicate "less than" between two boxes. Need for interval index
     * @param {Box} other_box - other box
     * @returns {boolean} - true if this box less than other box, false otherwise
     */
    less_than(other_box) {
        if (this.low.lessThan(other_box.low))
            return true;
        if (this.low.equalTo(other_box.low) && this.high.lessThan(other_box.high))
            return true;
        return false;
    }

    /**
     * Returns true if this box is equal to other box, false otherwise
     * @param {Box} other_box - query box
     * @returns {boolean}
     */
    equal_to(other_box) {
        return (this.low.equalTo(other_box.low) && this.high.equalTo(other_box.high));
    }

    output() {
        return this.clone();
    }

    comparable_less_than(pt1, pt2) {
        return pt1.lessThan(pt2);
    }

    /**
     * Set new values to the box object
     * @param {number} xmin - mininal x coordinate
     * @param {number} ymin - minimal y coordinate
     * @param {number} xmax - maximal x coordinate
     * @param {number} ymax - maximal y coordinate
     */
    set(xmin, ymin, xmax, ymax) {
        this.xmin = xmin;
        this.ymin = ymin;
        this.xmax = xmax;
        this.ymax = ymax;
    }

    /**
     * Return new extended box
     * @param {number} extension - positive number, how much to extend the box
     * @returns {Box}
     */
    extend(extension) {
        if (extension <= 0) return this.clone();
        return new Box(
            this.xmin - extension,
            this.ymin - extension,
            this.xmax + extension,
            this.ymax + extension
        )
    }

    /**
     * Transform box into array of points from low left corner in counterclockwise
     * @returns {Point[]}
     */
    toPoints() {
        return [
            new Flatten.Point(this.xmin, this.ymin),
            new Flatten.Point(this.xmax, this.ymin),
            new Flatten.Point(this.xmax, this.ymax),
            new Flatten.Point(this.xmin, this.ymax)
        ];
    }

    /**
     * Transform box into array of segments from low left corner in counterclockwise
     * @returns {Segment[]}
     */
    toSegments() {
        let pts = this.toPoints();
        return [
            new Flatten.Segment(pts[0], pts[1]),
            new Flatten.Segment(pts[1], pts[2]),
            new Flatten.Segment(pts[2], pts[3]),
            new Flatten.Segment(pts[3], pts[0])
        ];
    }

    /**
     * Box rotation is not supported
     * Attempt to rotate box throws error
     * @param {number} angle - angle in radians
     * @param {Point} [center=(0,0)] center
     */
    rotate(angle, center = new Flatten.Point()) {
            throw Errors.OPERATION_IS_NOT_SUPPORTED
    }

    /**
     * Return new box transformed using affine transformation matrix
     * New box is a bounding box of transformed corner points
     * @param {Matrix} m - affine transformation matrix
     * @returns {Box}
     */
    transform(m = new Flatten.Matrix()) {
        const transformed_points = this.toPoints().map(pt => pt.transform(m));
        return transformed_points.reduce(
            (new_box, pt) => new_box.merge(pt.box), new Box())
    }

    /**
     * Return true if box contains shape: no point of shape lies outside the box
     * @param {AnyShape} shape - test shape
     * @returns {boolean}
     */
    contains(shape) {
        if (shape instanceof Flatten.Point) {
            return (shape.x >= this.xmin) && (shape.x <= this.xmax) && (shape.y >= this.ymin) && (shape.y <= this.ymax);
        }

        if (shape instanceof Flatten.Segment) {
            return shape.vertices.every(vertex => this.contains(vertex))
        }

        if (shape instanceof Flatten.Box) {
            return shape.toSegments().every(segment => this.contains(segment))
        }

        if (shape instanceof Flatten.Circle) {
            return this.contains(shape.box)
        }

        if (shape instanceof Flatten.Arc) {
            return shape.vertices.every(vertex => this.contains(vertex)) &&
                this.toSegments().every(segment => intersectSegment2Arc(segment, shape).length === 0)
        }

        if (shape instanceof Flatten.Line || shape instanceof Flatten.Ray) {
            return false
        }

        if (shape instanceof Flatten.Multiline) {
            return shape.toShapes().every(shape => this.contains(shape))
        }

        if (shape instanceof Flatten.Polygon) {
            return this.contains(shape.box)
        }
    }

    /**
     * Calculate distance and shortest segment from box to shape and return as array [distance, shortest segment]
     * @param {Shape} shape Shape of the one of supported types Point, Line, Circle, Segment, Arc, Polygon or Planar Set
     * @returns {number} distance from box to shape
     * @returns {Segment} shortest segment between box and shape (started at box, ended at shape)
     */
    distanceTo(shape) {
        const distanceInfos = this.toSegments()
          .map(segment => segment.distanceTo(shape));
        let shortestDistanceInfo = [
          Number.MAX_SAFE_INTEGER,
          null,
        ];
        distanceInfos.forEach(distanceInfo => {
          if (distanceInfo[0] < shortestDistanceInfo[0]) {
            shortestDistanceInfo = distanceInfo;
          }
        });
        return shortestDistanceInfo;
    }

    get name() {
        return "box"
    }

    /**
     * Return string to draw box in svg
     * @param {Object} attrs - an object with attributes of svg rectangle element
     * @returns {string}
     */
    svg(attrs = {}) {
        const width = this.xmax - this.xmin;
        const height = this.ymax - this.ymin;
        return `\n<rect x="${this.xmin}" y="${this.ymin}" width="${width}" height="${height}"
                ${convertToString({fill: "none", ...attrs})} />`;
    };
}

Flatten.Box = Box;
/**
 * Shortcut to create new box
 * @param args
 * @returns {Box}
 */
const box = (...args) => new Flatten.Box(...args);
Flatten.box = box;

/**
 * Created by Alex Bol on 3/17/2017.
 */


/**
 * Class representing an edge of polygon. Edge shape may be Segment or Arc.
 * Each edge contains references to the next and previous edges in the face of the polygon.
 *
 * @type {Edge}
 */
class Edge {
    /**
     * Construct new instance of edge
     * @param {Shape} shape Shape of type Segment or Arc
     */
    constructor(shape) {
        /**
         * Shape of the edge: Segment or Arc
         * @type {Segment|Arc}
         */
        this.shape = shape;
        /**
         * Pointer to the next edge in the face
         * @type {Edge}
         */
        this.next = undefined;
        /**
         * Pointer to the previous edge in the face
         * @type {Edge}
         */
        this.prev = undefined;
        /**
         * Pointer to the face containing this edge
         * @type {Face}
         */
        this.face = undefined;
        /**
         * "Arc distance" from the face start
         * @type {number}
         */
        this.arc_length = 0;
        /**
         * Start inclusion flag (inside/outside/boundary)
         * @type {*}
         */
        this.bvStart = undefined;
        /**
         * End inclusion flag (inside/outside/boundary)
         * @type {*}
         */
        this.bvEnd = undefined;
        /**
         * Edge inclusion flag (Flatten.INSIDE, Flatten.OUTSIDE, Flatten.BOUNDARY)
         * @type {*}
         */
        this.bv = undefined;
        /**
         * Overlap flag for boundary edge (Flatten.OVERLAP_SAME/Flatten.OVERLAP_OPPOSITE)
         * @type {*}
         */
        this.overlap = undefined;
    }

    /**
     * Get edge start point
     */
    get start() {
        return this.shape.start;
    }

    /**
     * Get edge end point
     */
    get end() {
        return this.shape.end;
    }

    /**
     * Get edge length
     */
    get length() {
        return this.shape.length;
    }

    /**
     * Get bounding box of the edge
     * @returns {Box}
     */
    get box() {
        return this.shape.box;
    }

    get isSegment() {
        return this.shape instanceof Flatten.Segment;
    }

    get isArc() {
        return this.shape instanceof Flatten.Arc;
    }

    get isLine() {
        return this.shape instanceof Flatten.Line;
    }

    get isRay() {
        return this.shape instanceof Flatten.Ray
    }

    /**
     * Get middle point of the edge
     * @returns {Point}
     */
    middle() {
        return this.shape.middle();
    }

    /**
     * Get point at given length
     * @param {number} length - The length along the edge
     * @returns {Point}
     */
    pointAtLength(length) {
        return this.shape.pointAtLength(length);
    }

    /**
     * Returns true if point belongs to the edge, false otherwise
     * @param {Point} pt - test point
     */
    contains(pt) {
        return this.shape.contains(pt);
    }

    /**
     * Set inclusion flag of the edge with respect to another polygon
     * Inclusion flag is one of Flatten.INSIDE, Flatten.OUTSIDE, Flatten.BOUNDARY
     * @param polygon
     */
    setInclusion(polygon) {
        if (this.bv !== undefined) return this.bv;

        if (this.shape instanceof Flatten.Line || this.shape instanceof Flatten.Ray) {
            this.bv = Flatten.OUTSIDE;
            return this.bv;
        }

        if (this.bvStart === undefined) {
            this.bvStart = ray_shoot(polygon, this.start);
        }
        if (this.bvEnd === undefined) {
            this.bvEnd = ray_shoot(polygon, this.end);
        }
        /* At least one end outside - the whole edge outside */
        if (this.bvStart === Flatten.OUTSIDE || this.bvEnd == Flatten.OUTSIDE) {
            this.bv = Flatten.OUTSIDE;
        }
        /* At least one end inside - the whole edge inside */
        else if (this.bvStart === Flatten.INSIDE || this.bvEnd == Flatten.INSIDE) {
            this.bv = Flatten.INSIDE;
        }
        /* Both are boundary - check the middle point */
        else {
            let bvMiddle = ray_shoot(polygon, this.middle());
            // let boundary = this.middle().distanceTo(polygon)[0] < 10*Flatten.DP_TOL;
            // let bvMiddle = boundary ? Flatten.BOUNDARY : ray_shoot(polygon, this.middle());
            this.bv = bvMiddle;
        }
        return this.bv;
    }

    /**
     * Set overlapping between two coincident boundary edges
     * Overlapping flag is one of Flatten.OVERLAP_SAME or Flatten.OVERLAP_OPPOSITE
     * @param edge
     */
    setOverlap(edge) {
        let flag = undefined;
        let shape1 = this.shape;
        let shape2 = edge.shape;

        if (shape1 instanceof Flatten.Segment && shape2 instanceof Flatten.Segment) {
            if (shape1.start.equalTo(shape2.start) && shape1.end.equalTo(shape2.end)) {
                flag = Flatten.OVERLAP_SAME;
            } else if (shape1.start.equalTo(shape2.end) && shape1.end.equalTo(shape2.start)) {
                flag = Flatten.OVERLAP_OPPOSITE;
            }
        } else if (shape1 instanceof Flatten.Arc && shape2 instanceof Flatten.Arc) {
            if (shape1.start.equalTo(shape2.start) && shape1.end.equalTo(shape2.end) && /*shape1.counterClockwise === shape2.counterClockwise &&*/
                shape1.middle().equalTo(shape2.middle())) {
                flag = Flatten.OVERLAP_SAME;
            } else if (shape1.start.equalTo(shape2.end) && shape1.end.equalTo(shape2.start) && /*shape1.counterClockwise !== shape2.counterClockwise &&*/
                shape1.middle().equalTo(shape2.middle())) {
                flag = Flatten.OVERLAP_OPPOSITE;
            }
        } else if (shape1 instanceof Flatten.Segment && shape2 instanceof Flatten.Arc ||
            shape1 instanceof Flatten.Arc && shape2 instanceof Flatten.Segment) {
            if (shape1.start.equalTo(shape2.start) && shape1.end.equalTo(shape2.end) && shape1.middle().equalTo(shape2.middle())) {
                flag = Flatten.OVERLAP_SAME;
            } else if (shape1.start.equalTo(shape2.end) && shape1.end.equalTo(shape2.start) && shape1.middle().equalTo(shape2.middle())) {
                flag = Flatten.OVERLAP_OPPOSITE;
            }
        }

        /* Do not update overlap flag if already set on previous chain */
        if (this.overlap === undefined) this.overlap = flag;
        if (edge.overlap === undefined) edge.overlap = flag;
    }

    svg() {
        if (this.shape instanceof Flatten.Segment) {
            return ` L${this.shape.end.x},${this.shape.end.y}`;
        } else if (this.shape instanceof Flatten.Arc) {
            let arc = this.shape;
            let largeArcFlag;
            let sweepFlag = arc.counterClockwise ? "1" : "0";

            // Draw full circe arc as special case: split it into two half-circles
            if (Flatten.Utils.EQ(arc.sweep, 2 * Math.PI)) {
                let sign = arc.counterClockwise ? 1 : -1;
                let halfArc1 = new Flatten.Arc(arc.pc, arc.r, arc.startAngle, arc.startAngle + sign * Math.PI, arc.counterClockwise);
                let halfArc2 = new Flatten.Arc(arc.pc, arc.r, arc.startAngle + sign * Math.PI, arc.endAngle, arc.counterClockwise);

                largeArcFlag = "0";

                return ` A${halfArc1.r},${halfArc1.r} 0 ${largeArcFlag},${sweepFlag} ${halfArc1.end.x},${halfArc1.end.y}
                    A${halfArc2.r},${halfArc2.r} 0 ${largeArcFlag},${sweepFlag} ${halfArc2.end.x},${halfArc2.end.y}`
            } else {
                largeArcFlag = arc.sweep <= Math.PI ? "0" : "1";

                return ` A${arc.r},${arc.r} 0 ${largeArcFlag},${sweepFlag} ${arc.end.x},${arc.end.y}`;
            }
        }
    }

    toJSON() {
        return this.shape.toJSON();
    }
}
Flatten.Edge = Edge;

/**
 * Class implements circular bidirectional linked list <br/>
 * LinkedListElement - object of any type that has properties next and prev.
 */
class CircularLinkedList extends LinkedList {
    constructor(first, last) {
        super(first, last);
        this.setCircularLinks();
    }

    setCircularLinks() {
        if (this.isEmpty()) return;
        this.last.next = this.first;
        this.first.prev = this.last;
    }

    [Symbol.iterator]() {
        let element = undefined;
        return {
            next: () => {
                let value = element ? element : this.first;
                let done = this.first ? (element ? element === this.first : false) : true;
                element = value ? value.next : undefined;
                return {value: value, done: done};
            }
        };
    };

    /**
     * Append new element to the end of the list
     * @param {LinkedListElement} element - new element to be appended
     * @returns {CircularLinkedList}
     */
    append(element) {
        super.append(element);
        this.setCircularLinks();
        return this;
    }

    /**
     * Insert new element to the list after elementBefore
     * @param {LinkedListElement} newElement - new element to be inserted
     * @param {LinkedListElement} elementBefore - element in the list to insert after it
     * @returns {CircularLinkedList}
     */
    insert(newElement, elementBefore) {
        super.insert(newElement, elementBefore);
        this.setCircularLinks();
        return this;
    }

    /**
     * Remove element from the list
     * @param {LinkedListElement} element - element to be removed from the list
     * @returns {CircularLinkedList}
     */
    remove(element) {
        super.remove(element);
        // this.setCircularLinks();
        return this;
    }
}

/**
 * Created by Alex Bol on 3/17/2017.
 */


/**
 * Class representing a face (closed loop) in a [polygon]{@link Flatten.Polygon} object.
 * Face is a circular bidirectionally linked list of [edges]{@link Flatten.Edge}.
 * Face object cannot be instantiated with a constructor.
 * Instead, use [polygon.addFace()]{@link Flatten.Polygon#addFace} method.
 * <br/>
 * Note, that face only set entry point to the linked list of edges but does not contain edges by itself.
 * Container of edges is a property of the polygon object. <br/>
 *
 * @example
 * // Face implements "next" iterator which enables to iterate edges in for loop:
 * for (let edge of face) {
 *      console.log(edge.shape.length)     // do something
 * }
 *
 * // Instead, it is possible to iterate edges as linked list, starting from face.first:
 * let edge = face.first;
 * do {
 *   console.log(edge.shape.length);   // do something
 *   edge = edge.next;
 * } while (edge != face.first)
 */
class Face extends CircularLinkedList {
    constructor(polygon, ...args) {
        super();            // construct empty list of edges
        /**
         * Reference to the first edge in face
         */
        // this.first;
        /**
         * Reference to the last edge in face
         */
        // this.last;

        this._box = undefined;  // new Box();
        this._orientation = undefined;

        if (args.length === 0) {
            return;
        }

        /* If passed an array it supposed to be:
         1) array of shapes that performs close loop or
         2) array of points that performs set of vertices
         */
        if (args.length === 1) {
            if (args[0] instanceof Array) {
                // let argsArray = args[0];
                let shapes = args[0];  // argsArray[0];
                if (shapes.length === 0)
                    return;

                /* array of Flatten.Points */
                if (shapes.every((shape) => {return shape instanceof Flatten.Point})) {
                    let segments = Face.points2segments(shapes);
                    this.shapes2face(polygon.edges, segments);
                }
                /* array of points as pairs of numbers */
                else if (shapes.every((shape) => {return shape instanceof Array && shape.length === 2})) {
                    let points = shapes.map((shape) => new Flatten.Point(shape[0],shape[1]));
                    let segments = Face.points2segments(points);
                    this.shapes2face(polygon.edges, segments);
                }
                /* array of segments ot arcs */
                else if (shapes.every((shape) => {
                    return (shape instanceof Flatten.Segment || shape instanceof Flatten.Arc)
                })) {
                    this.shapes2face(polygon.edges, shapes);
                }
                // this is from JSON.parse object
                else if (shapes.every((shape) => {
                    return (shape.name === "segment" || shape.name === "arc")
                })) {
                    let flattenShapes = [];
                    for (let shape of shapes) {
                        let flattenShape;
                        if (shape.name === "segment") {
                            flattenShape = new Flatten.Segment(shape);
                        } else {
                            flattenShape = new Flatten.Arc(shape);
                        }
                        flattenShapes.push(flattenShape);
                    }
                    this.shapes2face(polygon.edges, flattenShapes);
                }
            }
            /* Create new face and copy edges into polygon.edges set */
            else if (args[0] instanceof Face) {
                let face = args[0];
                this.first = face.first;
                this.last = face.last;
                for (let edge of face) {
                    polygon.edges.add(edge);
                }
            }
            /* Instantiate face from a circle in CCW orientation */
            else if (args[0] instanceof Flatten.Circle) {
                this.shapes2face(polygon.edges, [args[0].toArc(CCW)]);
            }
            /* Instantiate face from a box in CCW orientation */
            else if (args[0] instanceof Flatten.Box) {
                let box = args[0];
                this.shapes2face(polygon.edges, [
                    new Flatten.Segment(new Flatten.Point(box.xmin, box.ymin), new Flatten.Point(box.xmax, box.ymin)),
                    new Flatten.Segment(new Flatten.Point(box.xmax, box.ymin), new Flatten.Point(box.xmax, box.ymax)),
                    new Flatten.Segment(new Flatten.Point(box.xmax, box.ymax), new Flatten.Point(box.xmin, box.ymax)),
                    new Flatten.Segment(new Flatten.Point(box.xmin, box.ymax), new Flatten.Point(box.xmin, box.ymin))
                ]);
            }
        }
        /* If passed two edges, consider them as start and end of the face loop */
        /* THIS METHOD WILL BE USED BY BOOLEAN OPERATIONS */
        /* Assume that edges already copied to polygon.edges set in the clip algorithm !!! */
        if (args.length === 2 && args[0] instanceof Flatten.Edge && args[1] instanceof Flatten.Edge) {
            this.first = args[0];                          // first edge in face or undefined
            this.last = args[1];                           // last edge in face or undefined
            this.last.next = this.first;
            this.first.prev = this.last;

            // set arc length
            this.setArcLength();

            // this.box = this.getBox();
            // this.orientation = this.getOrientation();      // face direction cw or ccw
        }
    }

    /**
     * Return array of edges from first to last
     * @returns {PolygonEdge[]}
     */
    get edges() {
        return this.toArray();
    }

    /**
     * Return array of vertices from first to last
     * @returns {Point[]}
     */
    get vertices() {
        return this.edges.map(edge => edge.shape.start.clone());
    }

    /**
     * Return array of shapes which comprise the face
     * @returns {Array<Segment | Arc>}
     */
    get shapes() {
        return this.edges.map(edge => edge.shape.clone());
    }

    /**
     * Return bounding box of the face
     * @returns {Box}
     */
    get box() {
        if (this._box === undefined) {
            let box = new Flatten.Box();
            for (let edge of this) {
                box = box.merge(edge.box);
            }
            this._box = box;
        }
        return this._box;
    }

    /**
     * Get all edges length
     * @returns {number}
     */
    get perimeter() {
        return this.last.arc_length + this.last.length
    }

    /**
     * Get point on face boundary at given length
     * @param {number} length - The length along the face boundary
     * @returns {Point}
     */
    pointAtLength(length) {
        if (length > this.perimeter || length < 0) return null;
        let point = null;
        for (let edge of this) {
            if (length >= edge.arc_length &&
                (edge === this.last || length < edge.next.arc_length)) {
                point = edge.pointAtLength(length - edge.arc_length);
                break;
            }
        }
        return point;
    }

    static points2segments(points) {
        let segments = [];
        for (let i = 0; i < points.length; i++) {
            // skip zero length segment
            if (points[i].equalTo(points[(i + 1) % points.length]))
                continue;
            segments.push(new Flatten.Segment(points[i], points[(i + 1) % points.length]));
        }
        return segments;
    }

    shapes2face(edges, shapes) {
        for (let shape of shapes) {
            let edge = new Flatten.Edge(shape);
            this.append(edge);
            // this.box = this.box.merge(shape.box);
            edges.add(edge);
        }
        // this.orientation = this.getOrientation();              // face direction cw or ccw
    }

    /**
     * Append edge after the last edge of the face (and before the first edge). <br/>
     * @param {Edge} edge - Edge to be appended to the linked list
     * @returns {Face}
     */
    append(edge) {
        super.append(edge);
        // set arc length
        this.setOneEdgeArcLength(edge);
        edge.face = this;
        // edges.add(edge);      // Add new edges into edges container
        return this;
    }

    /**
     * Insert edge newEdge into the linked list after the edge edgeBefore <br/>
     * @param {Edge} newEdge - Edge to be inserted into linked list
     * @param {Edge} edgeBefore - Edge to insert newEdge after it
     * @returns {Face}
     */
    insert(newEdge, edgeBefore) {
        super.insert(newEdge, edgeBefore);
        // set arc length
        this.setOneEdgeArcLength(newEdge);
        newEdge.face = this;
        return this;
    }

    /**
     * Remove the given edge from the linked list of the face <br/>
     * @param {Edge} edge - Edge to be removed
     * @returns {Face}
     */
    remove(edge) {
        super.remove(edge);
        // Recalculate arc length
        this.setArcLength();
        return this;
    }

    /**
     * Merge current edge with the next edge. Given edge will be extended,
     * next edge after it will be removed. The distortion of the polygon
     * is on the responsibility of the user of this method
     * @param {Edge} edge - edge to be extended
     * @returns {Face}
     */
    merge_with_next_edge(edge) {
        edge.shape.end.x = edge.next.shape.end.x;
        edge.shape.end.y = edge.next.shape.end.y;
        this.remove(edge.next);
        return this;
    }

    /**
     * Reverse orientation of the face: first edge become last and vice a verse,
     * all edges starts and ends swapped, direction of arcs inverted. If face was oriented
     * clockwise, it becomes counterclockwise and vice versa
     */
    reverse() {
        // collect edges in revert order with reverted shapes
        let edges = [];
        let edge_tmp = this.last;
        do {
            // reverse shape
            edge_tmp.shape = edge_tmp.shape.reverse();
            edges.push(edge_tmp);
            edge_tmp = edge_tmp.prev;
        } while (edge_tmp !== this.last);

        // restore linked list
        this.first = undefined;
        this.last = undefined;
        for (let edge of edges) {
            if (this.first === undefined) {
                edge.prev = edge;
                edge.next = edge;
                this.first = edge;
                this.last = edge;
            } else {
                // append to end
                edge.prev = this.last;
                this.last.next = edge;

                // update edge to be last
                this.last = edge;

                // restore circular links
                this.last.next = this.first;
                this.first.prev = this.last;

            }
            // set arc length
            this.setOneEdgeArcLength(edge);
        }

        // Recalculate orientation, if set
        if (this._orientation !== undefined) {
            this._orientation = undefined;
            this._orientation = this.orientation();
        }
    }


    /**
     * Set arc_length property for each of the edges in the face.
     * Arc_length of the edge it the arc length from the first edge of the face
     */
    setArcLength() {
        for (let edge of this) {
            this.setOneEdgeArcLength(edge);
            edge.face = this;
        }
    }

    setOneEdgeArcLength(edge) {
        if (edge === this.first) {
            edge.arc_length = 0.0;
        } else {
            edge.arc_length = edge.prev.arc_length + edge.prev.length;
        }
    }

    /**
     * Returns the absolute value of the area of the face
     * @returns {number}
     */
    area() {
        return Math.abs(this.signedArea());
    }

    /**
     * Returns signed area of the simple face.
     * Face is simple if it has no self intersections that change its orientation.
     * Then the area will be positive if the orientation of the face is clockwise,
     * and negative if orientation is counterclockwise.
     * It may be zero if polygon is degenerated.
     * @returns {number}
     */
    signedArea() {
        let sArea = 0;
        let ymin = this.box.ymin;
        for (let edge of this) {
            sArea += edge.shape.definiteIntegral(ymin);
        }
        return sArea;
    }

    /**
     * Return face orientation: one of Flatten.ORIENTATION.CCW, Flatten.ORIENTATION.CW, Flatten.ORIENTATION.NOT_ORIENTABLE <br/>
     * According to Green theorem the area of a closed curve may be calculated as double integral,
     * and the sign of the integral will be defined by the direction of the curve.
     * When the integral ("signed area") will be negative, direction is counterclockwise,
     * when positive - clockwise and when it is zero, polygon is not orientable.
     * See {@link https://mathinsight.org/greens_theorem_find_area}
     * @returns {number}
     */
    orientation() {
        if (this._orientation === undefined) {
            let area = this.signedArea();
            if (Flatten.Utils.EQ_0(area)) {
                this._orientation = ORIENTATION.NOT_ORIENTABLE;
            } else if (Flatten.Utils.LT(area, 0)) {
                this._orientation = ORIENTATION.CCW;
            } else {
                this._orientation = ORIENTATION.CW;
            }
        }
        return this._orientation;
    }

    /**
     * Returns true if face of the polygon is simple (no self-intersection points found)
     * NOTE: this method is incomplete because it does not exclude touching points.
     * Self intersection test should check if polygon change orientation in the test point.
     * @param {PlanarSet} edges - reference to polygon edges to provide search index
     * @returns {boolean}
     */
    isSimple(edges) {
        let ip = Face.getSelfIntersections(this, edges, true);
        return ip.length === 0;
    }

    static getSelfIntersections(face, edges, exitOnFirst = false) {
        let int_points = [];

        // calculate intersections
        for (let edge1 of face) {

            // request edges of polygon in the box of edge1
            let resp = edges.search(edge1.box);

            // for each edge2 in response
            for (let edge2 of resp) {

                // Skip itself
                if (edge1 === edge2)
                    continue;

                // Skip is edge2 belongs to another face
                if (edge2.face !== face)
                    continue;

                // Skip next and previous edge if both are segment (if one of them arc - calc intersection)
                if (edge1.shape instanceof Flatten.Segment && edge2.shape instanceof Flatten.Segment &&
                    (edge1.next === edge2 || edge1.prev === edge2))
                    continue;

                // calculate intersections between edge1 and edge2
                let ip = edge1.shape.intersect(edge2.shape);

                // for each intersection point
                for (let pt of ip) {

                    // skip start-end connections
                    if (pt.equalTo(edge1.start) && pt.equalTo(edge2.end) && edge2 === edge1.prev)
                        continue;
                    if (pt.equalTo(edge1.end) && pt.equalTo(edge2.start) && edge2 === edge1.next)
                        continue;

                    int_points.push(pt);

                    if (exitOnFirst)
                        break;
                }

                if (int_points.length > 0 && exitOnFirst)
                    break;
            }

            if (int_points.length > 0 && exitOnFirst)
                break;

        }
        return int_points;
    }

    /**
     * Returns edge which contains given point
     * @param {Point} pt - test point
     * @returns {Edge}
     */
    findEdgeByPoint(pt) {
        let edgeFound;
        for (let edge of this) {
            if (pt.equalTo(edge.shape.start)) continue
            if (pt.equalTo(edge.shape.end) || edge.shape.contains(pt)) {
                edgeFound = edge;
                break;
            }
        }
        return edgeFound;
    }

    /**
     * Returns new polygon created from one face
     * @returns {Polygon}
     */
    toPolygon() {
        return new Flatten.Polygon(this.shapes);
    }

    toJSON() {
        return this.edges.map(edge => edge.toJSON());
    }

    /**
     * Returns string to be assigned to "d" attribute inside defined "path"
     * @returns {string}
     */
    svg() {
        let svgStr = `M${this.first.start.x},${this.first.start.y}`;
        for (let edge of this) {
            svgStr += edge.svg();
        }
        svgStr += ` z`;
        return svgStr;
    }

}

Flatten.Face = Face;

/**
 * Class representing a ray (a half-infinite line).
 * @type {Ray}
 */
class Ray extends Shape {
    /**
     * Ray may be constructed by setting an <b>origin</b> point and a <b>normal</b> vector, so that any point <b>x</b>
     * on a ray fit an equation: <br />
     *  (<b>x</b> - <b>origin</b>) * <b>vector</b> = 0 <br />
     * Ray defined by constructor is a right semi-infinite line with respect to the normal vector <br/>
     * If normal vector is omitted ray is considered horizontal (normal vector is (0,1)). <br/>
     * Don't be confused: direction of the normal vector is orthogonal to the ray <br/>
     * @param {Point} pt - start point
     * @param {Vector} norm - normal vector
     */
    constructor(...args) {
        super();
        this.pt = new Flatten.Point();
        this.norm = new Flatten.Vector(0,1);

        if (args.length === 0) {
            return;
        }

        if (args.length >= 1 && args[0] instanceof Flatten.Point) {
            this.pt = args[0].clone();
        }

        if (args.length === 1) {
            return;
        }

        if (args.length === 2 && args[1] instanceof Flatten.Vector) {
            this.norm = args[1].clone();
            return;
        }

        throw Errors.ILLEGAL_PARAMETERS;
    }

    /**
     * Return new cloned instance of ray
     * @returns {Ray}
     */
    clone() {
        return new Ray(this.pt, this.norm);
    }

    /**
     * Slope of the ray - angle in radians between ray and axe x from 0 to 2PI
     * @returns {number} - slope of the line
     */
    get slope() {
        let vec = new Flatten.Vector(this.norm.y, -this.norm.x);
        return vec.slope;
    }

    /**
     * Returns half-infinite bounding box of the ray
     * @returns {Box} - bounding box
     */
    get box() {
        let slope = this.slope;
        return new Flatten.Box(
            slope > Math.PI/2 && slope < 3*Math.PI/2 ? Number.NEGATIVE_INFINITY : this.pt.x,
            slope >= 0 && slope <= Math.PI ? this.pt.y : Number.NEGATIVE_INFINITY,
            slope >= Math.PI/2 && slope <= 3*Math.PI/2 ? this.pt.x : Number.POSITIVE_INFINITY,
            slope >= Math.PI && slope <= 2*Math.PI || slope === 0 ? this.pt.y : Number.POSITIVE_INFINITY
        )
    }

    /**
     * Return ray start point
     * @returns {Point} - ray start point
     */
    get start() {
        return this.pt;
    }

    /**
     * Ray has no end point?
     * @returns {undefined}
     */
    get end() {return undefined;}

    /**
     * Return positive infinity number as length
     * @returns {number}
     */
    get length() {return Number.POSITIVE_INFINITY;}

    /**
     * Returns true if point belongs to ray
     * @param {Point} pt Query point
     * @returns {boolean}
     */
    contains(pt) {
        if (this.pt.equalTo(pt)) {
            return true;
        }
        /* Ray contains point if vector to point is orthogonal to the ray normal vector
            and cross product from vector to point is positive */
        let vec = new Flatten.Vector(this.pt, pt);
        return Flatten.Utils.EQ_0(this.norm.dot(vec)) && Flatten.Utils.GE(vec.cross(this.norm),0);
    }

    /**
     * Return coordinate of the point that lies on the ray in the transformed
     * coordinate system where center is the projection of the point(0,0) to
     * the line containing this ray and axe y is collinear to the normal vector. <br/>
     * This method assumes that point lies on the ray
     * @param {Point} pt - point on a ray
     * @returns {number}
     */
    coord(pt) {
        return vector$1(pt.x, pt.y).cross(this.norm);
    }

    /**
     * Split ray with point and return array of segment and new ray
     * @param {Point} pt
     * @returns [Segment,Ray]
     */
    split(pt) {
        if (!this.contains(pt))
            return [];

        if (this.pt.equalTo(pt)) {
            return [this]
        }

        return [
            new Flatten.Segment(this.pt, pt),
            new Flatten.Ray(pt, this.norm)
        ]
    }

    /**
     * Returns array of intersection points between ray and another shape
     * @param {Shape} shape - Shape to intersect with ray
     * @returns {Point[]} array of intersection points
     */
    intersect(shape) {
        if (shape instanceof Flatten.Point) {
            return this.contains(shape) ? [shape] : [];
        }

        if (shape instanceof Flatten.Segment) {
            return intersectRay2Segment(this, shape);
        }

        if (shape instanceof Flatten.Arc) {
            return intersectRay2Arc(this, shape);
        }

        if (shape instanceof Flatten.Line) {
            return intersectRay2Line(this, shape);
        }

        if (shape instanceof Flatten.Ray) {
            return intersectRay2Ray(this, shape)
        }

        if (shape instanceof Flatten.Circle) {
            return intersectRay2Circle(this, shape);
        }

        if (shape instanceof Flatten.Box) {
            return intersectRay2Box(this, shape);
        }

        if (shape instanceof Flatten.Polygon) {
            return  intersectRay2Polygon(this, shape);
        }

        if (shape instanceof Flatten.Multiline) {
            return intersectShape2Multiline(this, shape);
        }
    }

    /**
     * Return new line rotated by angle
     * @param {number} angle - angle in radians
     * @param {Point} center - center of rotation
     */
    rotate(angle, center = new Flatten.Point()) {
        return new Flatten.Ray(
            this.pt.rotate(angle, center),
            this.norm.rotate(angle)
        )
    }

    /**
     * Return new ray transformed by affine transformation matrix
     * @param {Matrix} m - affine transformation matrix (a,b,c,d,tx,ty)
     * @returns {Ray}
     */
    transform(m) {
        return new Flatten.Ray(
            this.pt.transform(m),
            this.norm.clone()
        )
    }

    get name() {
        return "ray"
    }

    /**
     * Return string to draw svg segment representing ray inside given box
     * @param {Box} box Box representing drawing area
     * @param {Object} attrs - an object with attributes of svg segment element
     */
    svg(box, attrs = {}) {
        let line = new Flatten.Line(this.pt, this.norm);
        let ip = intersectLine2Box(line, box);
        ip = ip.filter( pt => this.contains(pt) );
        if (ip.length === 0 || ip.length === 2)
            return "";
        let segment = new Flatten.Segment(this.pt, ip[0]);
        return segment.svg(attrs);
    }

}

Flatten.Ray = Ray;

const ray = (...args) => new Flatten.Ray(...args);
Flatten.ray = ray;

/**
 * Created by Alex Bol on 3/15/2017.
 */


/**
 * Class representing a polygon.<br/>
 * Polygon in FlattenJS is a multipolygon comprised from a set of [faces]{@link Flatten.Face}. <br/>
 * Face, in turn, is a closed loop of [edges]{@link Flatten.Edge}, where edge may be segment or circular arc<br/>
 * @type {Polygon}
 */
let Polygon$1 = class Polygon {
    /**
     * Constructor creates new instance of polygon. With no arguments new polygon is empty.<br/>
     * Constructor accepts as argument array that define loop of shapes
     * or array of arrays in case of multi polygon <br/>
     * Loop may be defined in different ways: <br/>
     * - array of shapes of type Segment or Arc <br/>
     * - array of points (Flatten.Point) <br/>
     * - array of numeric pairs which represent points <br/>
     * - box or circle object <br/>
     * Alternatively, it is possible to use polygon.addFace method
     * @param {args} - array of shapes or array of arrays
     */
    constructor() {
        /**
         * Container of faces (closed loops), may be empty
         * @type {PlanarSet}
         */
        this.faces = new Flatten.PlanarSet();
        /**
         * Container of edges
         * @type {PlanarSet}
         */
        this.edges = new Flatten.PlanarSet();

        /* It may be array of something that may represent one loop (face) or
         array of arrays that represent multiple loops
         */
        let args = [...arguments];
        if (args.length === 1 &&
            ((args[0] instanceof Array && args[0].length > 0) ||
                args[0] instanceof Flatten.Circle || args[0] instanceof Flatten.Box)) {
            let argsArray = args[0];
            if (args[0] instanceof Array && args[0].every((loop) => {
                return loop instanceof Array
            })) {
                if (argsArray.every(el => {
                    return el instanceof Array && el.length === 2 && typeof (el[0]) === "number" && typeof (el[1]) === "number"
                })) {
                    this.faces.add(new Flatten.Face(this, argsArray));    // one-loop polygon as array of pairs of numbers
                } else {
                    for (let loop of argsArray) {   // multi-loop polygon
                        /* Check extra level of nesting for GeoJSON-style multi polygons */
                        if (loop instanceof Array && loop[0] instanceof Array &&
                            loop[0].every(el => {
                                return el instanceof Array && el.length === 2 && typeof (el[0]) === "number" && typeof (el[1]) === "number"
                            })) {
                            for (let loop1 of loop) {
                                this.faces.add(new Flatten.Face(this, loop1));
                            }
                        } else {
                            this.faces.add(new Flatten.Face(this, loop));
                        }
                    }
                }
            } else {
                this.faces.add(new Flatten.Face(this, argsArray));    // one-loop polygon
            }
        }
    }

    /**
     * (Getter) Returns bounding box of the polygon
     * @returns {Box}
     */
    get box() {
        return [...this.faces].reduce((acc, face) => acc.merge(face.box), new Flatten.Box());
    }

    /**
     * (Getter) Returns array of vertices
     * @returns {Array}
     */
    get vertices() {
        return [...this.faces].flatMap(face => face.vertices);
        // return [...this.edges].map(edge => edge.start);
    }

    /**
     * Create new cloned instance of the polygon
     * @returns {Polygon}
     */
    clone() {
        let polygon = new Polygon();
        for (let face of this.faces) {
            polygon.addFace(face.shapes);
        }
        return polygon;
    }

    /**
     * Create a polygon from an array of polygons. Each polygon is an outer face with all containing inner faces
     * @param polygons
     * @returns {Polygon}
     */
    createFromArray(polygons) {
        const newPolygon = new Polygon();
        polygons.forEach(polygon => [...polygon.faces].forEach(face => newPolygon.addFace(face.shapes)));
        return newPolygon;
    }

    /**
     * Return true is polygon has no edges or faces
     * @returns {boolean}
     */
    isEmpty() {
        return this.edges.size === 0 || this.faces.size === 0;
    }

    /**
     * Return true if polygon is valid for boolean operations
     * Polygon is valid if <br/>
     * 1. All faces are simple polygons (there are no self-intersected polygons) <br/>
     * 2. All faces are orientable and there is no island inside island or hole inside hole - TODO <br/>
     * 3. There is no intersections between faces (excluding touching) - TODO <br/>
     * @returns {boolean}
     */
    isValid() {
        let valid = true;
        // 1. Polygon is invalid if at least one face is not simple
        for (let face of this.faces) {
            if (!face.isSimple(this.edges)) {
                valid = false;
                break;
            }
        }
        // 2. TODO: check if no island inside island and no hole inside hole
        // 3. TODO: check the there is no intersection between faces
        return valid;
    }

    /**
     * Returns area of the polygon. Area of an island will be added, area of a hole will be subtracted
     * @returns {number}
     */
    area() {
        let signedArea = [...this.faces].reduce((acc, face) => acc + face.signedArea(), 0);
        return Math.abs(signedArea);
    }

    /**
     * Add a new face to polygon. Returns added face
     * @param {Point[]|Segment[]|Arc[]|Circle|Box} args -  new face may be create with one of the following ways: <br/>
     * 1) array of points that describe closed path (edges are segments) <br/>
     * 2) array of shapes (segments and arcs) which describe closed path <br/>
     * 3) circle - will be added as counterclockwise arc <br/>
     * 4) box - will be added as counterclockwise rectangle <br/>
     * You can chain method face.reverse() is you need to change direction of the creates face
     * @returns {Face}
     */
    addFace(...args) {
        let face = new Flatten.Face(this, ...args);
        this.faces.add(face);
        return face;
    }

    /**
     * Delete existing face from polygon
     * @param {Face} face Face to be deleted
     * @returns {boolean}
     */
    deleteFace(face) {
        for (let edge of face) {
            this.edges.delete(edge);
        }
        return this.faces.delete(face);
    }

    /**
     * Clear all faces and create new faces from edges
     */
    recreateFaces() {
        // Remove all faces
        this.faces.clear();
        for (let edge of this.edges) {
            edge.face = null;
        }

        // Restore faces
        let first;
        let unassignedEdgeFound = true;
        while (unassignedEdgeFound) {
            unassignedEdgeFound = false;
            for (let edge of this.edges) {
                if (edge.face === null) {
                    first = edge;
                    unassignedEdgeFound = true;
                    break;
                }
            }

            if (unassignedEdgeFound) {
                let last = first;
                do {
                    last = last.next;
                } while (last.next !== first)

                this.addFace(first, last);
            }
        }
    }

    /**
     * Delete chain of edges from the face.
     * @param {Face} face Face to remove chain
     * @param {Edge} edgeFrom Start of the chain of edges to be removed
     * @param {Edge} edgeTo End of the chain of edges to be removed
     */
    removeChain(face, edgeFrom, edgeTo) {
        // Special case: all edges removed
        if (edgeTo.next === edgeFrom) {
            this.deleteFace(face);
            return;
        }
        for (let edge = edgeFrom; edge !== edgeTo.next; edge = edge.next) {
            face.remove(edge);
            this.edges.delete(edge);      // delete from PlanarSet of edges and update index
            if (face.isEmpty()) {
                this.deleteFace(face);    // delete from PlanarSet of faces and update index
                break;
            }
        }
    }

    /**
     * Add point as a new vertex and split edge. Point supposed to belong to an edge.
     * When edge is split, new edge created from the start of the edge to the new vertex
     * and inserted before current edge.
     * Current edge is trimmed and updated.
     * Method returns new edge added. If no edge added, it returns edge before vertex
     * @param {Point} pt Point to be added as a new vertex
     * @param {Edge} edge Edge to be split with new vertex and then trimmed from start
     * @returns {Edge}
     */
    addVertex(pt, edge) {
        let shapes = edge.shape.split(pt);
        // if (shapes.length < 2) return;

        if (shapes[0] === null)   // point incident to edge start vertex, return previous edge
            return edge.prev;

        if (shapes[1] === null)   // point incident to edge end vertex, return edge itself
            return edge;

        let newEdge = new Flatten.Edge(shapes[0]);
        let edgeBefore = edge.prev;

        /* Insert first split edge into linked list after edgeBefore */
        edge.face.insert(newEdge, edgeBefore);

        // Remove old edge from edges container and 2d index
        this.edges.delete(edge);

        // Insert new edge to the edges container and 2d index
        this.edges.add(newEdge);

        // Update edge shape with second split edge keeping links
        edge.shape = shapes[1];

        // Add updated edge to the edges container and 2d index
        this.edges.add(edge);

        return newEdge;
    }

    /**
     * Merge given edge with next edge and remove vertex between them
     * @param {Edge} edge
     */
    removeEndVertex(edge) {
        const edge_next = edge.next;
        if (edge_next === edge) return
        edge.face.merge_with_next_edge(edge);
        this.edges.delete(edge_next);
    }

    /**
     * Cut polygon with multiline and return a new polygon
     * The cutting is done by intersection of multiline with edges of the polygon.
     * @param multiline
     * @returns {Polygon}
     */
    cut(multiline) {
        const polygons = this.splitToIslands();
        const result = polygons.flatMap(polygon => polygon._cutSingleIsland(multiline))
            .filter(polygon => polygon.isValid() && polygon.isEmpty() === false);
        return this.createFromArray(result);
    }

    /**
     * Cut polygon with multiline and return a new polygon
     * @param {Multiline} multiline
     * @returns {Polygon}
     */
    _cutSingleIsland(inputMultiline) {
        let newPoly = this.clone();
        const multiline = inputMultiline.clone();

        // smart intersections
        let intersections = {
            int_points1: [],
            int_points2: [],
            int_points1_sorted: [],
            int_points2_sorted: []
        };

        // intersect each edge of multiline with each edge of the polygon
        // and create smart intersections
        for (let edge1 of multiline.edges) {
            for (let edge2 of newPoly.edges) {
                let ip = intersectEdge2Edge(edge1, edge2);
                // for each intersection point
                for (let pt of ip) {
                    addToIntPoints(edge1, pt, intersections.int_points1);
                    addToIntPoints(edge2, pt, intersections.int_points2);
                }
            }
        }

        // No intersections - return a copy of the original polygon
        if (intersections.int_points1.length === 0)
            return newPoly;

        // sort smart intersections
        intersections.int_points1_sorted = getSortedArray(intersections.int_points1);
        intersections.int_points2_sorted = getSortedArray(intersections.int_points2);

        // split by intersection points
        splitByIntersections(multiline, intersections.int_points1_sorted);
        splitByIntersections(newPoly, intersections.int_points2_sorted);

        // filter duplicated intersection points
        filterDuplicatedIntersections(intersections);

        // sort intersection points again after filtering
        intersections.int_points1_sorted = getSortedArray(intersections.int_points1);
        intersections.int_points2_sorted = getSortedArray(intersections.int_points2);

        // initialize inclusion flags for edges of multiline incident to intersections
        initializeInclusionFlags(intersections.int_points1);

        // calculate inclusion flag for edges of multiline incident to intersections
        calculateInclusionFlags(intersections.int_points1, newPoly);

        // filter intersections between two edges that got same inclusion flag
        for (let int_point1 of intersections.int_points1_sorted) {
            if (int_point1.edge_before && int_point1.edge_after &&
                int_point1.edge_before.bv === int_point1.edge_after.bv) {
                intersections.int_points2[int_point1.id] = -1;   // to be filtered out
                int_point1.id = -1;                              // to be filtered out
            }
        }
        intersections.int_points1 = intersections.int_points1.filter( int_point => int_point.id >= 0);
        intersections.int_points2 = intersections.int_points2.filter( int_point => int_point.id >= 0);
        intersections.int_points1.forEach((int_point, index) => { int_point.id = index; });
        intersections.int_points2.forEach((int_point, index) => { int_point.id = index; });


        // No intersections left after filtering - return a copy of the original polygon
        if (intersections.int_points1.length === 0)
            return newPoly;

        // sort intersection points 3d time after filtering
        intersections.int_points1_sorted = getSortedArray(intersections.int_points1);
        intersections.int_points2_sorted = getSortedArray(intersections.int_points2);

        // Add new inner edges between intersection points
        let int_point1_prev;
        let int_point1_curr;
        for (let i = 1; i <  intersections.int_points1_sorted.length; i++) {
            int_point1_curr = intersections.int_points1_sorted[i];
            int_point1_prev = intersections.int_points1_sorted[i-1];
            if (int_point1_curr.edge_before && int_point1_curr.edge_before.bv === INSIDE$2) {
                let edgeFrom = int_point1_prev.edge_after;
                let edgeTo = int_point1_curr.edge_before;
                let newEdges = multiline.getChain(edgeFrom, edgeTo);
                insertBetweenIntPoints(intersections.int_points2[int_point1_prev.id], intersections.int_points2[int_point1_curr.id], newEdges);
                newEdges.forEach(edge => newPoly.edges.add(edge));

                newEdges = newEdges.reverse().map(edge => new Flatten.Edge(edge.shape.reverse()));
                for (let k=0; k < newEdges.length-1; k++) {
                    newEdges[k].next = newEdges[k+1];
                    newEdges[k+1].prev = newEdges[k];
                }
                insertBetweenIntPoints(intersections.int_points2[int_point1_curr.id], intersections.int_points2[int_point1_prev.id], newEdges);
                newEdges.forEach(edge => newPoly.edges.add(edge));
            }

        }

        // Recreate faces
        newPoly.recreateFaces();

        return newPoly
    }

    /**
     * A special case of cut() function
     * The return is a polygon cut with line
     * @param {Line} line - cutting line
     * @returns {Polygon} newPoly - resulted polygon
     */
    cutWithLine(line) {
        let multiline = new Multiline$1([line]);
        return this.cut(multiline);
    }

    /**
     * Returns the first found edge of polygon that contains given point
     * If point is a vertex, return the edge where the point is an end vertex, not a start one
     * @param {Point} pt
     * @returns {Edge}
     */
    findEdgeByPoint(pt) {
        let edge;
        for (let face of this.faces) {
            edge = face.findEdgeByPoint(pt);
            if (edge !== undefined)
                break;
        }
        return edge;
    }

    /**
     * Split polygon into array of polygons, where each polygon is an outer face with all
     * containing inner faces
     * @returns {Flatten.Polygon[]}
     */
    splitToIslands() {
        if (this.isEmpty()) return [];      // return empty array if polygon is empty
        let polygons = this.toArray();      // split into array of one-loop polygons
        /* Sort polygons by area in descending order */
        polygons.sort((polygon1, polygon2) => polygon2.area() - polygon1.area());
        /* define orientation of the island by orientation of the first polygon in array */
        let orientation = [...polygons[0].faces][0].orientation();
        /* Create output array from polygons with same orientation as a first polygon (array of islands) */
        let newPolygons = polygons.filter(polygon => [...polygon.faces][0].orientation() === orientation);
        for (let polygon of polygons) {
            let face = [...polygon.faces][0];
            if (face.orientation() === orientation) continue;  // skip same orientation
            /* Proceed with opposite orientation */
            /* Look if any of island polygons contains tested polygon as a hole */
            for (let islandPolygon of newPolygons) {
                if (face.shapes.every(shape => islandPolygon.contains(shape))) {
                    islandPolygon.addFace(face.shapes);      // add polygon as a hole in islandPolygon
                    break;
                }
            }
        }
        // TODO: assert if not all polygons added into output
        return newPolygons;
    }

    /**
     * Rearrange polygon to ensure that all outer faces go first and all inner faces (holes) go after
     * @returns {Polygon}
     */
    rearrange() {
        if (this.faces.size <=1 ) return this.clone()
        const islands = this.splitToIslands();
        const newPolygon = new Polygon();
        islands.forEach(island => {
            island.faces.forEach((face) => newPolygon.addFace(face.shapes));
        });
        return newPolygon
    }

    /**
     * Helper method to get orientation of the polygon as the first face orientation
     * Assume that polygon is properly arranged and the first face is the outer face
     * @returns {Flatten.ORIENTATION.PolygonOrientationType}
     */
    orientation() {
        if (this.isEmpty()) return ORIENTATION.NOT_ORIENTABLE
        return [...this.faces][0].orientation();
    }

    /**
     * Helper method to check if face is outer face of the polygon
     * @param face
     * @returns {boolean}
     */
    isOuter(face) {
        return face.orientation() === this.orientation();
    }

    /**
     * Helper method to check if a polygon is a multi-polygon (has more than one outer face)
     * @returns {boolean}
     */
    isMultiPolygon() {
        let outerCounter = 0;
        this.faces.forEach(face => {
            if (this.isOuter(face)) outerCounter++;
        });
        return outerCounter > 1
    }

    /**
     * Reverse orientation of all faces to opposite
     * @returns {Polygon}
     */
    reverse() {
        for (let face of this.faces) {
            face.reverse();
        }
        return this;
    }

    /**
     * Returns true if polygon contains shape: no point of shape lay outside of the polygon,
     * false otherwise
     * @param {Shape} shape - test shape
     * @returns {boolean}
     */
    contains(shape) {
        if (shape instanceof Flatten.Point) {
            let rel = ray_shoot(this, shape);
            return rel === INSIDE$2 || rel === BOUNDARY$1;
        } else {
            return cover(this, shape);
        }
    }

    /**
     * Return distance and shortest segment between polygon and other shape as array [distance, shortest_segment]
     * @param {Shape} shape Shape of one of the types Point, Circle, Line, Segment, Arc or Polygon
     * @returns {Number | Segment}
     */
    distanceTo(shape) {
        // let {Distance} = Flatten;

        if (shape instanceof Flatten.Point) {
            let [dist, shortest_segment] = Flatten.Distance.point2polygon(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [dist, shortest_segment];
        }

        if (shape instanceof Flatten.Circle ||
            shape instanceof Flatten.Line ||
            shape instanceof Flatten.Segment ||
            shape instanceof Flatten.Arc) {
            let [dist, shortest_segment] = Flatten.Distance.shape2polygon(shape, this);
            shortest_segment = shortest_segment.reverse();
            return [dist, shortest_segment];
        }

        /* this method is bit faster */
        if (shape instanceof Flatten.Polygon) {
            let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
            let dist, shortest_segment;

            for (let edge of this.edges) {
                // let [dist, shortest_segment] = Distance.shape2polygon(edge.shape, shape);
                let min_stop = min_dist_and_segment[0];
                [dist, shortest_segment] = Flatten.Distance.shape2planarSet(edge.shape, shape.edges, min_stop);
                if (Flatten.Utils.LT(dist, min_stop)) {
                    min_dist_and_segment = [dist, shortest_segment];
                }
            }
            return min_dist_and_segment;
        }
    }

    /**
     * Return array of intersection points between polygon and other shape
     * @param shape Shape of the one of supported types <br/>
     * @returns {Point[]}
     */
    intersect(shape) {
        if (shape instanceof Flatten.Point) {
            return this.contains(shape) ? [shape] : [];
        }

        if (shape instanceof Flatten.Line) {
            return intersectLine2Polygon(shape, this);
        }

        if (shape instanceof Flatten.Ray) {
            return intersectRay2Polygon(shape, this);
        }

        if (shape instanceof Flatten.Circle) {
            return intersectCircle2Polygon(shape, this);
        }

        if (shape instanceof Flatten.Segment) {
            return intersectSegment2Polygon(shape, this);
        }

        if (shape instanceof Flatten.Arc) {
            return intersectArc2Polygon(shape, this);
        }

        if (shape instanceof Flatten.Polygon) {
            return intersectPolygon2Polygon(shape, this);
        }

        if (shape instanceof Flatten.Multiline) {
            return intersectMultiline2Polygon(shape, this);
        }
    }

    /**
     * Returns new polygon translated by vector vec
     * @param {Vector} vec
     * @returns {Polygon}
     */
    translate(vec) {
        let newPolygon = new Polygon();
        for (let face of this.faces) {
            newPolygon.addFace(face.shapes.map(shape => shape.translate(vec)));
        }
        return newPolygon;
    }

    /**
     * Return new polygon rotated by given angle around given point
     * If point omitted, rotate around origin (0,0)
     * Positive value of angle defines rotation counterclockwise, negative - clockwise
     * @param {number} angle - rotation angle in radians
     * @param {Point} center - rotation center, default is (0,0)
     * @returns {Polygon} - new rotated polygon
     */
    rotate(angle = 0, center = new Flatten.Point()) {
        let newPolygon = new Polygon();
        for (let face of this.faces) {
            newPolygon.addFace(face.shapes.map(shape => shape.rotate(angle, center)));
        }
        return newPolygon;
    }

    /**
     * Return new polygon with coordinates multiplied by scaling factor
     * @param {number} sx - x-axis scaling factor
     * @param {number} sy - y-axis scaling factor
     * @returns {Polygon}
     */
    scale(sx, sy) {
        let newPolygon = new Polygon();
        for (let face of this.faces) {
            newPolygon.addFace(face.shapes.map(shape => shape.scale(sx, sy)));
        }
        return newPolygon;
    }

    /**
     * Return new polygon transformed using affine transformation matrix
     * @param {Matrix} matrix - affine transformation matrix
     * @returns {Polygon} - new polygon
     */
    transform(matrix = new Flatten.Matrix()) {
        let newPolygon = new Polygon();
        for (let face of this.faces) {
            newPolygon.addFace(face.shapes.map(shape => shape.transform(matrix)));
        }
        return newPolygon;
    }

    /**
     * This method returns an object that defines how data will be
     * serialized when called JSON.stringify() method
     * @returns {Object}
     */
    toJSON() {
        return [...this.faces].map(face => face.toJSON());
    }

    /**
     * Transform all faces into array of polygons
     * @returns {Flatten.Polygon[]}
     */
    toArray() {
        return [...this.faces].map(face => face.toPolygon());
    }

    /**
     * Return string to be assigned to 'd' attribute of <path> element
     * @returns {string}
     */
    dpath() {
        return [...this.faces].reduce((acc, face) => acc + face.svg(), "")
    }

    /**
     * Return string to draw polygon in svg
     * @param attrs  - an object with attributes for svg path element
     * @returns {string}
     */
    svg(attrs = {}) {
        let svgStr = `\n<path ${convertToString({fillRule: "evenodd", fill: "lightcyan", ...attrs})} d="`;
        for (let face of this.faces) {
            svgStr += `\n${face.svg()}` ;
        }
        svgStr += `" >\n</path>`;
        return svgStr;
    }
};

Flatten.Polygon = Polygon$1;

/**
 * Shortcut method to create new polygon
 */
const polygon = (...args) => new Flatten.Polygon(...args);
Flatten.polygon = polygon;

const {Circle, Line, Point: Point$2, Vector, Utils} = Flatten;
/**
 * Class Inversion represent operator of inversion in circle
 * Inversion is a transformation of the Euclidean plane that maps generalized circles
 * (where line is considered as a circle with infinite radius) into generalized circles
 * See also https://en.wikipedia.org/wiki/Inversive_geometry and
 * http://mathworld.wolfram.com/Inversion.html <br/>
 * @type {Inversion}
 */
class Inversion {
    /**
     * Inversion constructor
     * @param {Circle} inversion_circle inversion circle
     */
    constructor(inversion_circle) {
        this.circle = inversion_circle;
    }


    get inversion_circle() {
        return this.circle;
    }

    static inversePoint(inversion_circle, point) {
        const v = new Vector(inversion_circle.pc, point);
        const k2 = inversion_circle.r * inversion_circle.r;
        const len2 = v.dot(v);
        const reflected_point = Utils.EQ_0(len2) ?
            new Point$2(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY) :
            inversion_circle.pc.translate(v.multiply(k2 / len2));
        return reflected_point;
    }

    static inverseCircle(inversion_circle, circle) {
        const dist = inversion_circle.pc.distanceTo(circle.pc)[0];
        if (Utils.EQ(dist, circle.r)) {     // Circle passing through inversion center mapped into line
            let d = (inversion_circle.r * inversion_circle.r) / (2 * circle.r);
            let v = new Vector(inversion_circle.pc, circle.pc);
            v = v.normalize();
            let pt = inversion_circle.pc.translate(v.multiply(d));

            return new Line(pt, v);
        } else {                           // Circle not passing through inversion center - map into another circle */
            /* Taken from http://mathworld.wolfram.com */
            let v = new Vector(inversion_circle.pc, circle.pc);
            let s = inversion_circle.r * inversion_circle.r / (v.dot(v) - circle.r * circle.r);
            let pc = inversion_circle.pc.translate(v.multiply(s));
            let r = Math.abs(s) * circle.r;

            return new Circle(pc, r);
        }
    }

    static inverseLine(inversion_circle, line) {
        const [dist, shortest_segment] = inversion_circle.pc.distanceTo(line);
        if (Utils.EQ_0(dist)) {            // Line passing through inversion center, is mapping to itself
            return line.clone();
        } else {                           // Line not passing through inversion center is mapping into circle
            let r = inversion_circle.r * inversion_circle.r / (2 * dist);
            let v = new Vector(inversion_circle.pc, shortest_segment.end);
            v = v.multiply(r / dist);
            return new Circle(inversion_circle.pc.translate(v), r);
        }
    }

    inverse(shape) {
        if (shape instanceof Point$2) {
            return Inversion.inversePoint(this.circle, shape);
        }
        else if (shape instanceof Circle) {
            return Inversion.inverseCircle(this.circle, shape);
        }
        else if (shape instanceof Line) {
            return Inversion.inverseLine(this.circle, shape);
        }
    }
}
Flatten.Inversion = Inversion;

/**
 * Shortcut to create inversion operator
 * @param circle
 * @returns {Inversion}
 */
const inversion = (circle) => new Flatten.Inversion(circle);
Flatten.inversion = inversion;

class Distance {
    /**
     * Calculate distance and shortest segment between points
     * @param pt1
     * @param pt2
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static point2point(pt1, pt2) {
        return pt1.distanceTo(pt2);
    }

    /**
     * Calculate distance and shortest segment between point and line
     * @param pt
     * @param line
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static point2line(pt, line) {
        let closest_point = pt.projectionOn(line);
        let vec = new Flatten.Vector(pt, closest_point);
        return [vec.length, new Flatten.Segment(pt, closest_point)];
    }

    /**
     * Calculate distance and shortest segment between point and circle
     * @param pt
     * @param circle
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static point2circle(pt, circle) {
        let [dist2center, shortest_dist] = pt.distanceTo(circle.center);
        if (Flatten.Utils.EQ_0(dist2center)) {
            return [circle.r, new Flatten.Segment(pt, circle.toArc().start)];
        } else {
            let dist = Math.abs(dist2center - circle.r);
            let v = new Flatten.Vector(circle.pc, pt).normalize().multiply(circle.r);
            let closest_point = circle.pc.translate(v);
            return [dist, new Flatten.Segment(pt, closest_point)];
        }
    }

    /**
     * Calculate distance and shortest segment between point and segment
     * @param pt
     * @param segment
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static point2segment(pt, segment) {
        /* Degenerated case of zero-length segment */
        if (segment.start.equalTo(segment.end)) {
            return Distance.point2point(pt, segment.start);
        }

        let v_seg = new Flatten.Vector(segment.start, segment.end);
        let v_ps2pt = new Flatten.Vector(segment.start, pt);
        let v_pe2pt = new Flatten.Vector(segment.end, pt);
        let start_sp = v_seg.dot(v_ps2pt);
        /* dot product v_seg * v_ps2pt */
        let end_sp = -v_seg.dot(v_pe2pt);
        /* minus dot product v_seg * v_pe2pt */

        let dist;
        let closest_point;
        if (Flatten.Utils.GE(start_sp, 0) && Flatten.Utils.GE(end_sp, 0)) {    /* point inside segment scope */
            let v_unit = segment.tangentInStart(); // new Flatten.Vector(v_seg.x / this.length, v_seg.y / this.length);
            /* unit vector ||v_unit|| = 1 */
            dist = Math.abs(v_unit.cross(v_ps2pt));
            /* dist = abs(v_unit x v_ps2pt) */
            closest_point = segment.start.translate(v_unit.multiply(v_unit.dot(v_ps2pt)));
            return [dist, new Flatten.Segment(pt, closest_point)];
        } else if (start_sp < 0) {                             /* point is out of scope closer to ps */
            return pt.distanceTo(segment.start);
        } else {                                               /* point is out of scope closer to pe */
            return pt.distanceTo(segment.end);
        }
    };

    /**
     * Calculate distance and shortest segment between point and arc
     * @param pt
     * @param arc
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static point2arc(pt, arc) {
        let circle = new Flatten.Circle(arc.pc, arc.r);
        let dist_and_segment = [];
        let dist, shortest_segment;
        [dist, shortest_segment] = Distance.point2circle(pt, circle);
        if (shortest_segment.end.on(arc)) {
            dist_and_segment.push(Distance.point2circle(pt, circle));
        }
        dist_and_segment.push(Distance.point2point(pt, arc.start));
        dist_and_segment.push(Distance.point2point(pt, arc.end));

        Distance.sort(dist_and_segment);

        return dist_and_segment[0];
    }

    /**
     * Calculate distance and shortest segment between point and edge
     * @param pt
     * @param edge
     * @returns {[number, Flatten.Segment]}
     */
    static point2edge(pt, edge) {
        return edge.shape instanceof Flatten.Segment ?
            Distance.point2segment(pt, edge.shape) :
            Distance.point2arc(pt, edge.shape);
    }

    /**
     * Calculate distance and shortest segment between segment and line
     * @param seg
     * @param line
     * @returns {[number, Flatten.Segment]}
     */
    static segment2line(seg, line) {
        let ip = seg.intersect(line);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];   // distance = 0, closest point is the first point
        }
        let dist_and_segment = [];
        dist_and_segment.push(Distance.point2line(seg.start, line));
        dist_and_segment.push(Distance.point2line(seg.end, line));

        Distance.sort(dist_and_segment);
        return dist_and_segment[0];

    }

    /**
     * Calculate distance and shortest segment between two segments
     * @param seg1
     * @param seg2
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static segment2segment(seg1, seg2) {
        let ip = intersectSegment2Segment(seg1, seg2);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];   // distance = 0, closest point is the first point
        }

        // Seg1 and seg2 not intersected
        let dist_and_segment = [];
        let dist_tmp, shortest_segment_tmp;
        [dist_tmp, shortest_segment_tmp] = Distance.point2segment(seg2.start, seg1);
        dist_and_segment.push([dist_tmp, shortest_segment_tmp.reverse()]);
        [dist_tmp, shortest_segment_tmp] = Distance.point2segment(seg2.end, seg1);
        dist_and_segment.push([dist_tmp, shortest_segment_tmp.reverse()]);
        dist_and_segment.push(Distance.point2segment(seg1.start, seg2));
        dist_and_segment.push(Distance.point2segment(seg1.end, seg2));

        Distance.sort(dist_and_segment);
        return dist_and_segment[0];
    }

    /**
     * Calculate distance and shortest segment between segment and circle
     * @param seg
     * @param circle
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static segment2circle(seg, circle) {
        /* Case 1 Segment and circle intersected. Return the first point and zero distance */
        let ip = seg.intersect(circle);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        // No intersection between segment and circle

        /* Case 2. Distance to projection of center point to line bigger than radius
         * And projection point belong to segment
          * Then measure again distance from projection to circle and return it */
        let line = new Flatten.Line(seg.ps, seg.pe);
        let [dist, shortest_segment] = Distance.point2line(circle.center, line);
        if (Flatten.Utils.GE(dist, circle.r) && shortest_segment.end.on(seg)) {
            return Distance.point2circle(shortest_segment.end, circle);
        }
        /* Case 3. Otherwise closest point is one of the end points of the segment */
        else {
            let [dist_from_start, shortest_segment_from_start] = Distance.point2circle(seg.start, circle);
            let [dist_from_end, shortest_segment_from_end] = Distance.point2circle(seg.end, circle);
            return Flatten.Utils.LT(dist_from_start, dist_from_end) ?
                [dist_from_start, shortest_segment_from_start] :
                [dist_from_end, shortest_segment_from_end];
        }
    }

    /**
     * Calculate distance and shortest segment between segment and arc
     * @param seg
     * @param arc
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static segment2arc(seg, arc) {
        /* Case 1 Segment and arc intersected. Return the first point and zero distance */
        let ip = seg.intersect(arc);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        // No intersection between segment and arc
        let line = new Flatten.Line(seg.ps, seg.pe);
        let circle = new Flatten.Circle(arc.pc, arc.r);

        /* Case 2. Distance to projection of center point to line bigger than radius AND
         * projection point belongs to segment AND
           * distance from projection point to circle belongs to arc  =>
           * return this distance from projection to circle */
        let [dist_from_center, shortest_segment_from_center] = Distance.point2line(circle.center, line);
        if (Flatten.Utils.GE(dist_from_center, circle.r) && shortest_segment_from_center.end.on(seg)) {
            let [dist_from_projection, shortest_segment_from_projection] =
                Distance.point2circle(shortest_segment_from_center.end, circle);
            if (shortest_segment_from_projection.end.on(arc)) {
                return [dist_from_projection, shortest_segment_from_projection];
            }
        }
        /* Case 3. Otherwise closest point is one of the end points of the segment */
        let dist_and_segment = [];
        dist_and_segment.push(Distance.point2arc(seg.start, arc));
        dist_and_segment.push(Distance.point2arc(seg.end, arc));

        let dist_tmp, segment_tmp;
        [dist_tmp, segment_tmp] = Distance.point2segment(arc.start, seg);
        dist_and_segment.push([dist_tmp, segment_tmp.reverse()]);

        [dist_tmp, segment_tmp] = Distance.point2segment(arc.end, seg);
        dist_and_segment.push([dist_tmp, segment_tmp.reverse()]);

        Distance.sort(dist_and_segment);
        return dist_and_segment[0];
    }

    /**
     * Calculate distance and shortest segment between two circles
     * @param circle1
     * @param circle2
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static circle2circle(circle1, circle2) {
        let ip = circle1.intersect(circle2);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        // Case 1. Concentric circles. Convert to arcs and take distance between two arc starts
        if (circle1.center.equalTo(circle2.center)) {
            let arc1 = circle1.toArc();
            let arc2 = circle2.toArc();
            return Distance.point2point(arc1.start, arc2.start);
        } else {
            // Case 2. Not concentric circles
            let line = new Flatten.Line(circle1.center, circle2.center);
            let ip1 = line.intersect(circle1);
            let ip2 = line.intersect(circle2);

            let dist_and_segment = [];

            dist_and_segment.push(Distance.point2point(ip1[0], ip2[0]));
            dist_and_segment.push(Distance.point2point(ip1[0], ip2[1]));
            dist_and_segment.push(Distance.point2point(ip1[1], ip2[0]));
            dist_and_segment.push(Distance.point2point(ip1[1], ip2[1]));

            Distance.sort(dist_and_segment);
            return dist_and_segment[0];
        }
    }

    /**
     * Calculate distance and shortest segment between two circles
     * @param circle
     * @param line
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static circle2line(circle, line) {
        let ip = circle.intersect(line);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        let [dist_from_center, shortest_segment_from_center] = Distance.point2line(circle.center, line);
        let [dist, shortest_segment] = Distance.point2circle(shortest_segment_from_center.end, circle);
        shortest_segment = shortest_segment.reverse();
        return [dist, shortest_segment];
    }

    /**
     * Calculate distance and shortest segment between arc and line
     * @param arc
     * @param line
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static arc2line(arc, line) {
        /* Case 1 Line and arc intersected. Return the first point and zero distance */
        let ip = line.intersect(arc);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        let circle = new Flatten.Circle(arc.center, arc.r);

        /* Case 2. Distance to projection of center point to line bigger than radius AND
         * projection point belongs to segment AND
           * distance from projection point to circle belongs to arc  =>
           * return this distance from projection to circle */
        let [dist_from_center, shortest_segment_from_center] = Distance.point2line(circle.center, line);
        if (Flatten.Utils.GE(dist_from_center, circle.r)) {
            let [dist_from_projection, shortest_segment_from_projection] =
                Distance.point2circle(shortest_segment_from_center.end, circle);
            if (shortest_segment_from_projection.end.on(arc)) {
                return [dist_from_projection, shortest_segment_from_projection];
            }
        } else {
            let dist_and_segment = [];
            dist_and_segment.push(Distance.point2line(arc.start, line));
            dist_and_segment.push(Distance.point2line(arc.end, line));

            Distance.sort(dist_and_segment);
            return dist_and_segment[0];
        }
    }

    /**
     * Calculate distance and shortest segment between arc and circle
     * @param arc
     * @param circle2
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static arc2circle(arc, circle2) {
        let ip = arc.intersect(circle2);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        let circle1 = new Flatten.Circle(arc.center, arc.r);

        let [dist, shortest_segment] = Distance.circle2circle(circle1, circle2);
        if (shortest_segment.start.on(arc)) {
            return [dist, shortest_segment];
        } else {
            let dist_and_segment = [];

            dist_and_segment.push(Distance.point2circle(arc.start, circle2));
            dist_and_segment.push(Distance.point2circle(arc.end, circle2));

            Distance.sort(dist_and_segment);

            return dist_and_segment[0];
        }
    }

    /**
     * Calculate distance and shortest segment between two arcs
     * @param arc1
     * @param arc2
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static arc2arc(arc1, arc2) {
        let ip = arc1.intersect(arc2);
        if (ip.length > 0) {
            return [0, new Flatten.Segment(ip[0], ip[0])];
        }

        let circle1 = new Flatten.Circle(arc1.center, arc1.r);
        let circle2 = new Flatten.Circle(arc2.center, arc2.r);

        let [dist, shortest_segment] = Distance.circle2circle(circle1, circle2);
        if (shortest_segment.start.on(arc1) && shortest_segment.end.on(arc2)) {
            return [dist, shortest_segment];
        } else {
            let dist_and_segment = [];

            let dist_tmp, segment_tmp;

            [dist_tmp, segment_tmp] = Distance.point2arc(arc1.start, arc2);
            if (segment_tmp.end.on(arc2)) {
                dist_and_segment.push([dist_tmp, segment_tmp]);
            }

            [dist_tmp, segment_tmp] = Distance.point2arc(arc1.end, arc2);
            if (segment_tmp.end.on(arc2)) {
                dist_and_segment.push([dist_tmp, segment_tmp]);
            }

            [dist_tmp, segment_tmp] = Distance.point2arc(arc2.start, arc1);
            if (segment_tmp.end.on(arc1)) {
                dist_and_segment.push([dist_tmp, segment_tmp.reverse()]);
            }

            [dist_tmp, segment_tmp] = Distance.point2arc(arc2.end, arc1);
            if (segment_tmp.end.on(arc1)) {
                dist_and_segment.push([dist_tmp, segment_tmp.reverse()]);
            }

            [dist_tmp, segment_tmp] = Distance.point2point(arc1.start, arc2.start);
            dist_and_segment.push([dist_tmp, segment_tmp]);

            [dist_tmp, segment_tmp] = Distance.point2point(arc1.start, arc2.end);
            dist_and_segment.push([dist_tmp, segment_tmp]);

            [dist_tmp, segment_tmp] = Distance.point2point(arc1.end, arc2.start);
            dist_and_segment.push([dist_tmp, segment_tmp]);

            [dist_tmp, segment_tmp] = Distance.point2point(arc1.end, arc2.end);
            dist_and_segment.push([dist_tmp, segment_tmp]);

            Distance.sort(dist_and_segment);

            return dist_and_segment[0];
        }
    }

    /**
     * Calculate distance and shortest segment between point and polygon
     * @param point
     * @param polygon
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static point2polygon(point, polygon) {
        let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
        for (let edge of polygon.edges) {
            let [dist, shortest_segment] = Distance.point2edge(point, edge);
            if (Flatten.Utils.LT(dist, min_dist_and_segment[0])) {
                min_dist_and_segment = [dist, shortest_segment];
            }
        }
        return min_dist_and_segment;
    }

    static shape2polygon(shape, polygon) {
        let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
        for (let edge of polygon.edges) {
            let [dist, shortest_segment] = shape.distanceTo(edge.shape);
            if (Flatten.Utils.LT(dist, min_dist_and_segment[0])) {
                min_dist_and_segment = [dist, shortest_segment];
            }
        }
        return min_dist_and_segment;
    }

    /**
     * Calculate distance and shortest segment between two polygons
     * @param polygon1
     * @param polygon2
     * @returns {[number, Flatten.Segment]} - distance and shortest segment
     */
    static polygon2polygon(polygon1, polygon2) {
        let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
        for (let edge1 of polygon1.edges) {
            for (let edge2 of polygon2.edges) {
                let [dist, shortest_segment] = edge1.shape.distanceTo(edge2.shape);
                if (Flatten.Utils.LT(dist, min_dist_and_segment[0])) {
                    min_dist_and_segment = [dist, shortest_segment];
                }
            }
        }
        return min_dist_and_segment;
    }

    /**
     * Returns [mindist, maxdist] array of squared minimal and maximal distance between boxes
     * Minimal distance by x is
     *    (box2.xmin - box1.xmax), if box1 is left to box2
     *    (box1.xmin - box2.xmax), if box2 is left to box1
     *    0,                       if box1 and box2 are intersected by x
     * Minimal distance by y is defined in the same way
     *
     * Maximal distance is estimated as a sum of squared dimensions of the merged box
     *
     * @param box1
     * @param box2
     * @returns {Number | Number} - minimal and maximal distance
     */
    static box2box_minmax(box1, box2) {
        let mindist_x = Math.max(Math.max(box1.xmin - box2.xmax, 0), Math.max(box2.xmin - box1.xmax, 0));
        let mindist_y = Math.max(Math.max(box1.ymin - box2.ymax, 0), Math.max(box2.ymin - box1.ymax, 0));
        let mindist = mindist_x * mindist_x + mindist_y * mindist_y;

        let box = box1.merge(box2);
        let dx = box.xmax - box.xmin;
        let dy = box.ymax - box.ymin;
        let maxdist = dx * dx + dy * dy;

        return [mindist, maxdist];
    }

    static minmax_tree_process_level(shape, level, min_stop, tree) {
        // Calculate minmax distance to each shape in current level
        // Insert result into the interval tree for further processing
        // update min_stop with maxdist, it will be the new stop distance
        let mindist, maxdist;
        for (let node of level) {

            // [mindist, maxdist] = Distance.box2box_minmax(shape.box, node.max);
            // if (Flatten.Utils.GT(mindist, min_stop))
            //     continue;

            // Estimate min-max dist to the shape stored in the node.items, using node.item.key which is shape's box
            [mindist, maxdist] = Distance.box2box_minmax(shape.box, node.item.key);
            for (let value of node.item.values) {
                if (value instanceof Flatten.Edge) {
                    tree.insert([mindist, maxdist], value.shape);
                } else {
                    tree.insert([mindist, maxdist], value);
                }
            }

            if (Flatten.Utils.LT(maxdist, min_stop)) {
                min_stop = maxdist;                       // this will be the new distance estimation
            }
        }

        if (level.length === 0)
            return min_stop;

        // Calculate new level from left and right children of the current
        let new_level_left = level.map(node => node.left.isNil() ? undefined : node.left).filter(node => node !== undefined);
        let new_level_right = level.map(node => node.right.isNil() ? undefined : node.right).filter(node => node !== undefined);
        // Merge left and right subtrees and leave only relevant subtrees
        let new_level = [...new_level_left, ...new_level_right].filter(node => {
            // Node subtree quick reject, node.max is a subtree box
            let [mindist, maxdist] = Distance.box2box_minmax(shape.box, node.max);
            return (Flatten.Utils.LE(mindist, min_stop));
        });

        min_stop = Distance.minmax_tree_process_level(shape, new_level, min_stop, tree);
        return min_stop;
    }

    /**
     * Calculates sorted tree of [mindist, maxdist] intervals between query shape
     * and shapes of the planar set.
     * @param shape
     * @param set
     */
    static minmax_tree(shape, set, min_stop) {
        let tree = new IntervalTree();
        let level = [set.index.root];
        let squared_min_stop = min_stop < Number.POSITIVE_INFINITY ? min_stop * min_stop : Number.POSITIVE_INFINITY;
        squared_min_stop = Distance.minmax_tree_process_level(shape, level, squared_min_stop, tree);
        return tree;
    }

    static minmax_tree_calc_distance(shape, node, min_dist_and_segment) {
        let min_dist_and_segment_new, stop;
        if (node != null && !node.isNil()) {
            [min_dist_and_segment_new, stop] = Distance.minmax_tree_calc_distance(shape, node.left, min_dist_and_segment);

            if (stop) {
                return [min_dist_and_segment_new, stop];
            }

            if (Flatten.Utils.LT(min_dist_and_segment_new[0], Math.sqrt(node.item.key.low))) {
                return [min_dist_and_segment_new, true];   // stop condition
            }

            let [dist, shortest_segment] = Distance.distanceToArray(shape, node.item.values);
            if (Flatten.Utils.LT(dist, min_dist_and_segment_new[0])) {
                min_dist_and_segment_new = [dist, shortest_segment];
            }

            [min_dist_and_segment_new, stop] = Distance.minmax_tree_calc_distance(shape, node.right, min_dist_and_segment_new);

            return [min_dist_and_segment_new, stop];
        }

        return [min_dist_and_segment, false];
    }

    /**
     * Calculates distance between shape and Planar Set of shapes
     * @param shape
     * @param {PlanarSet} set
     * @param {Number} min_stop
     * @returns {*}
     */
    static shape2planarSet(shape, set, min_stop = Number.POSITIVE_INFINITY) {
        let min_dist_and_segment = [min_stop, new Flatten.Segment()];
        let stop = false;
        if (set instanceof Flatten.PlanarSet) {
            let tree = Distance.minmax_tree(shape, set, min_stop);
            [min_dist_and_segment, stop] = Distance.minmax_tree_calc_distance(shape, tree.root, min_dist_and_segment);
        }
        return min_dist_and_segment;
    }

    static sort(dist_and_segment) {
        dist_and_segment.sort((d1, d2) => {
            if (Flatten.Utils.LT(d1[0], d2[0])) {
                return -1;
            }
            if (Flatten.Utils.GT(d1[0], d2[0])) {
                return 1;
            }
            return 0;
        });
    }

    static distance(shape1, shape2) {
        return shape1.distanceTo(shape2);
    }

    static distanceToArray(shape1, shapes) {
        let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
        for (let shape2 of shapes) {
            let [dist, shortest_segment] = shape1.distanceTo(shape2);
            if (Flatten.Utils.LT(dist, min_dist_and_segment[0])) {
                min_dist_and_segment = [dist, shortest_segment];
            }
        }
        return min_dist_and_segment;
    }

    /**
     * Calculate distance and shortest segment any shape and multiline
     * @param shape
     * @param multiline
     * @returns {[number, Flatten.Segment]}
     */
    static shape2multiline(shape, multiline) {
        let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
        for (let edge of multiline) {
            let [dist, shortest_segment] = Distance.distance(shape, edge.shape);
            if (Flatten.Utils.LT(dist, min_dist_and_segment[0])) {
                min_dist_and_segment = [dist, shortest_segment];
            }
        }
        return min_dist_and_segment;
    }

    /**
     * Calculate distance and shortest segment between two multilines
     * @param multiline1
     * @param multiline2
     * @returns {[number, Flatten.Segment]}
     */
    static multiline2multiline(multiline1, multiline2) {
        let min_dist_and_segment = [Number.POSITIVE_INFINITY, new Flatten.Segment()];
        for (let edge1 of multiline1) {
            for (let edge2 of multiline2) {
                let [dist, shortest_segment] = Distance.distance(edge1.shape, edge2.shape);
                if (Flatten.Utils.LT(dist, min_dist_and_segment[0])) {
                    min_dist_and_segment = [dist, shortest_segment];
                }
            }
        }
        return min_dist_and_segment;
    }
}

Flatten.Distance = Distance;

const {Multiline, Point: Point$1, Segment, Polygon} = Flatten;

// POINT (30 10)
// MULTIPOINT (10 40, 40 30, 20 20, 30 10)
// LINESTRING (30 10, 10 30, 40 40)
// MULTILINESTRING ((10 10, 20 20, 10 40), (40 40, 30 30, 40 20, 30 10))
// MULTILINESTRING ((8503.732 4424.547, 8963.747 3964.532), (8963.747 3964.532, 8707.468 3708.253), (8707.468 3708.253, 8247.454 4168.268), (8247.454 4168.268, 8503.732 4424.547))
// POLYGON ((35 10, 45 45, 15 40, 10 20, 35 10), (20 30, 35 35, 30 20, 20 30))
// MULTIPOLYGON (((40 40, 20 45, 45 30, 40 40)), ((20 35, 10 30, 10 10, 30 5, 45 20, 20 35), (30 20, 20 15, 20 25, 30 20)))
// GEOMETRYCOLLECTION (POINT (0 0), LINESTRING (0 0, 1440 900), POLYGON ((0 0, 0 1024, 1024 1024, 1024 0, 0 0)))
// GEOMETRYCOLLECTION (POINT (40 10), LINESTRING (10 10, 20 20, 10 40), POLYGON ((40 40, 20 45, 45 30, 40 40)))

function parseSinglePoint(pointStr) {
    return new Point$1(pointStr.split(' ').map(Number))
}

function parseMultiPoint(multipointStr) {
    return multipointStr.split(', ').map(parseSinglePoint)
}

function parseLineString(lineStr) {
    const points = parseMultiPoint(lineStr);
    let segments = [];
    for (let i = 0; i < points.length-1;  i++) {
        segments.push(new Segment(points[i], points[i+1]));
    }
    return new Multiline(segments)
}

function parseMultiLineString(multilineStr) {
    const lineStrings = multilineStr.replace(/\(\(/, '').replace(/\)\)$/, '').split('), (');
    return lineStrings.map(parseLineString)
}

function parseSinglePolygon(polygonStr) {
    const facesStr = polygonStr.replace(/\(\(/, '').replace(/\)\)$/, '').split('), (');
    const polygon = new Polygon();
    let orientation;
    facesStr.forEach((facesStr, idx) => {
        let points = facesStr.split(', ').map(coordStr => {
            return new Point$1(coordStr.split(' ').map(Number))
        });
        const face = polygon.addFace(points);
        if (idx === 0) {
            orientation = face.orientation();
        }
        else {
            if (face.orientation() === orientation) {
                face.reverse();
            }
        }
    });
    return polygon
}

function parseMutliPolygon(multiPolygonString) {
    // const polygonStrings = multiPolygonString.split('?')
    // Split the string by the delimiter ")), ((" which separates the polygons
    const polygonStrings = multiPolygonString.split(/\)\), \(\(/).map(polygon => '((' + polygon + '))');

    const polygons = polygonStrings.map(parseSinglePolygon);
    const polygon = new Polygon();
    const faces = polygons.reduce((acc, polygon) => [...acc, ...polygon?.faces], []);
    faces.forEach(face => polygon.addFace([...face?.shapes]));
    return polygon;
}

function parsePolygon(wkt) {
    if (wkt.startsWith("POLYGON")) {
        const polygonStr = wkt.replace(/^POLYGON /, '');
        return parseSinglePolygon(polygonStr)
    }
    else {
        // const multiPolygonString = wkt.replace(/^MULTIPOLYGON \(/, '').replace(/\)$/, '').replace(/\)\), \(\(/,'))?((')
        const multiPolygonString = wkt.replace(/^MULTIPOLYGON \(\(\((.*)\)\)\)$/, '$1');
        return parseMutliPolygon(multiPolygonString)
    }
}

function parseArrayOfPoints(str) {
    const arr = str.split('\n').map(x => x.match(/\(([^)]+)\)/)[1]);
    return arr.map(parseSinglePoint)
}

function parseArrayOfLineStrings(str) {
    const arr = str.split('\n').map(x => x.match(/\(([^)]+)\)/)[1]);
    return arr.map(parseLineString).reduce((acc, x) => [...acc, ...x], [])
}

/**
 * Convert WKT string to array of Flatten shapes.
 * @param str
 * @returns {Point | Point[] | Multiline | Multiline[] | Polygon | Shape[] | null}
 */
function parseWKT(str) {
    if (str.startsWith("POINT")) {
        const pointStr = str.replace(/^POINT \(/, '').replace(/\)$/, '');
        return parseSinglePoint(pointStr)
    }
    else if (str.startsWith("MULTIPOINT")) {
        const multiPointStr = str.replace(/^MULTIPOINT \(/, '').replace(/\)$/, '');
        return parseMultiPoint(multiPointStr)
    }
    else if (str.startsWith("LINESTRING")) {
        const lineStr = str.replace(/^LINESTRING \(/, '').replace(/\)$/, '');
        return parseLineString(lineStr)
    }
    else if (str.startsWith("MULTILINESTRING")) {
        const multilineStr = str.replace(/^MULTILINESTRING /, '');
        return parseMultiLineString(multilineStr)
    }
    else if (str.startsWith("POLYGON") || str.startsWith("MULTIPOLYGON")) {
        return parsePolygon(str)
    }
    else if (str.startsWith("GEOMETRYCOLLECTION")) {
        // const regex = /(POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION) \([^\)]+\)/g
        /* Explanation:
(?<type>POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON):
This named group will capture the geometry type. The type label helps with understanding the structure but
 is not necessary unless you process the matches programmatically and want easy access to the geometry type.
\( and \): Match the opening and closing parentheses.
(?:[^\(\)]|\([^\)]*\))*: A non-capturing group that allows for:
[^\(\)]: Matching any character except parentheses, handling simple geometries.
|\([^\)]*\): Handling nested parentheses for geometries like POLYGON and MULTILINESTRING.
* after the non-capturing group: Allows for repeating the pattern zero or more times to match all contents between the outermost parentheses. */
        const regex = /(?<type>POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON) \((?:[^\(\)]|\([^\)]*\))*\)/g;
        const wktArray = str.match(regex);
        if (wktArray[0].startsWith('GEOMETRYCOLLECTION')) {
            wktArray[0] = wktArray[0].replace('GEOMETRYCOLLECTION (','');
        }
        const flArray = wktArray.map(parseWKT).map(x => x instanceof Array ? x : [x]);
        return flArray.reduce((acc, x) => [...acc, ...x], [])
    }
    else if (isArrayOfPoints(str)) {
        return parseArrayOfPoints(str)
    }
    else if (isArrayOfLines(str)) {
        return parseArrayOfLineStrings(str)
    }
    return []
}

function isArrayOfPoints(str) {
    return str.split('\n')?.every(str => str.includes('POINT'))
}

function isArrayOfLines(str) {
    return str.split('\n')?.every(str => str.includes('LINESTRING'))
}

/**
 * Return true if given string starts with one of WKT tags and possibly contains WKT string,
 * @param str
 * @returns {boolean}
 */
function isWktString(str) {
    return (
        str.startsWith("POINT") || isArrayOfPoints(str) ||
        str.startsWith("LINESTRING") || isArrayOfLines(str) ||
        str.startsWith("MULTILINESTRING") ||
        str.startsWith("POLYGON") ||
        str.startsWith("MULTIPOINT") ||
        str.startsWith("MULTIPOLYGON") ||
        str.startsWith("GEOMETRYCOLLECTION")
    )
}

Flatten.isWktString = isWktString;
Flatten.parseWKT = parseWKT;

/**
 * Created by Alex Bol on 2/18/2017.
 */


Flatten.BooleanOperations = BooleanOperations;
Flatten.Relations = Relations;

class TrackConnector {
    PointRoundingPrecision = 0.01;
    points = new Map();
    polygons = [];
    addTrack(track) {
        const edge = {
            drawing: track,
            from: track.start,
            to: track.end,
        };
        this.addEdge(edge);
    }
    addPolygon(poly) {
        this.polygons.push(poly);
    }
    // addArc(arc: Arc) {
    // 	this.addPoint(arc.)
    // }
    addEdge(obj) {
        obj.from = [
            TransformExports.roundToMultiple(obj.from[0], this.PointRoundingPrecision),
            TransformExports.roundToMultiple(obj.from[1], this.PointRoundingPrecision),
        ];
        obj.to = [
            TransformExports.roundToMultiple(obj.to[0], this.PointRoundingPrecision),
            TransformExports.roundToMultiple(obj.to[1], this.PointRoundingPrecision),
        ];
        {
            if (!this.points.has(obj.from[0])) {
                this.points.set(obj.from[0], new Map());
            }
            const xMap = this.points.get(obj.from[0]);
            if (!xMap.has(obj.from[1])) {
                xMap.set(obj.from[1], [obj]);
            }
            else {
                xMap.get(obj.from[1])?.push(obj);
            }
        }
        {
            if (!this.points.has(obj.to[0])) {
                this.points.set(obj.to[0], new Map());
            }
            const xMap = this.points.get(obj.to[0]);
            if (!xMap.has(obj.to[1])) {
                xMap.set(obj.to[1], [obj]);
            }
            else {
                xMap.get(obj.to[1])?.push(obj);
            }
        }
    }
    formsEnclosure() {
        let drawingCount = 0;
        for (const [, yMap] of this.points.entries()) {
            for (const [, drawings] of yMap.entries()) {
                drawingCount += 2;
                if (drawings.length !== 2) {
                    return false;
                }
            }
        }
        return drawingCount >= 2 || this.polygons.length > 0;
    }
    getLoops() {
        if (!this.formsEnclosure()) {
            console.warn('Shape must form enclosure');
            return [];
        }
        const loops = [];
        for (const [x, yMap] of this.points.entries()) {
            for (const [y, edges] of yMap.entries()) {
                let inLoop = false;
                for (const edge of edges) {
                    for (const loop of loops) {
                        if (loop.drawings.has(edge.drawing)) {
                            inLoop = true;
                            break;
                        }
                    }
                    if (inLoop) {
                        break;
                    }
                }
                if (inLoop) {
                    continue;
                }
                loops.push(this.calculateLoop(x, y));
            }
        }
        return loops.map((l) => l.positions);
    }
    calculateLoop(startX, startY) {
        const visitedEdges = [];
        const drawings = new Set();
        const visitedPositions = [];
        const visitedPoints = new Map();
        const pointsToVisit = [[startX, startY]];
        while (pointsToVisit.length > 0) {
            const point = pointsToVisit.splice(0, 1)[0];
            if (visitedPoints.get(point[0])?.has(point[1])) {
                for (const drawing of this.points.get(point[0]).get(point[1])) {
                    if (!drawings.has(drawing.drawing)) {
                        drawings.add(drawing.drawing);
                    }
                }
                continue;
            }
            if (visitedPoints.has(point[0])) {
                visitedPoints.get(point[0])?.add(point[1]);
            }
            else {
                visitedPoints.set(point[0], new Set([point[1]]));
            }
            visitedPositions.push(point);
            const edges = this.points.get(point[0]).get(point[1]);
            for (const edge of edges) {
                if (visitedEdges.includes(edge)) {
                    continue;
                }
                visitedEdges.push(edge);
                if (!drawings.has(edge.drawing)) {
                    drawings.add(edge.drawing);
                }
                if (!visitedPoints.get(edge.from[0])?.has(edge.from[1]) &&
                    !pointsToVisit.find((p) => p[0] === edge.from[0] && p[1] === edge.from[1])) {
                    pointsToVisit.splice(0, 0, edge.from);
                }
                if (!visitedPoints.get(edge.to[0])?.has(edge.to[1]) &&
                    !pointsToVisit.find((p) => p[0] === edge.to[0] && p[1] === edge.to[1])) {
                    pointsToVisit.splice(0, 0, edge.to);
                }
            }
        }
        return {
            drawings,
            positions: visitedPositions,
        };
    }
    getPoly() {
        const points = [];
        for (const [x, yMap] of this.points.entries()) {
            for (const [y] of yMap.entries()) {
                points.push([x, y]);
            }
        }
        const basePoly = new Flatten.Polygon(points);
        for (const poly of this.polygons) {
            basePoly.addFace(poly.points.map((p) => new Flatten.Point(p[0], p[1])));
        }
        return basePoly;
    }
    getPolygons() {
        const loops = this.getLoops();
        const basePoly = new Flatten.Polygon();
        for (const loop of loops) {
            basePoly.addFace(loop.map((p) => new Flatten.Point(p[0], p[1])));
        }
        for (const poly of this.polygons) {
            basePoly.addFace(poly.points.map((p) => new Flatten.Point(p[0], p[1])));
        }
        return basePoly.splitToIslands();
        // const points: XYPosition[] = [];
        // for (const [x, yMap] of this.points.entries()) {
        // 	for (const [y, drawings] of yMap.entries()) {
        // 		points.push([x, y]);
        // 	}
        // }
        // const basePoly = new Flatten.Polygon(points);
        // for (const poly of this.polygons) {
        // 	basePoly.addFace(poly.points.map(p => new Flatten.Point(p[0], p[1])));
        // }
        // return basePoly.splitToIslands();
    }
}

class ModelPreviewRenderer extends FullModelRenderer {
    edgePoly;
    edgeGraphic;
    maskGraphic;
    maskMask;
    static RenderDrawingToManager(manager, model, design, opts = { face: 'F' }) {
        switch (design.modelType) {
            case 'pcb':
                return ModelPreviewRenderer.RenderBoardToManager(manager, model, design, opts);
            default:
                throw new Error(`Unsupported model type for preview renderer: ${design.modelType}`);
        }
    }
    static RenderBoardToManager(manager, model, design, opts = { face: 'F' }) {
        const renderer = new ModelPreviewRenderer(manager, model, design, opts);
        renderer.allowMissingSourceLayers = true;
        if (!design) {
            // @ts-expect-error: design should be never if it's not defined.
            throw new Error(`Could not find design id '${design.designId}' in project`);
        }
        else if (design.modelType !== 'pcb') {
            throw new Error(`Design '${design.designId}' is not a pcb`);
        }
        const layers = model.layerSets[design.layerSet];
        const visibleLayers = layers.filter((l) => l.defaultVisible).map((l) => l.id);
        manager.setDefaultVisibleLayers(visibleLayers);
        manager.setLayerOrder([
            ...layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.SilkS),
            ...layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.Mask),
            ...layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.F || l.type === distExports.DesignModels.PcbLayerType.B),
            ...layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.Edge),
        ].map((l) => l.id));
        renderer.renderEdges(layers, design);
        renderer.generateMaskLayer(layers, design);
        layers.forEach((layer) => {
            if (layer.face !== opts.face ||
                ![
                    distExports.DesignModels.PcbLayerType.Edge,
                    distExports.DesignModels.PcbLayerType.F,
                    distExports.DesignModels.PcbLayerType.B,
                    distExports.DesignModels.PcbLayerType.Mask,
                    distExports.DesignModels.PcbLayerType.SilkS,
                ].includes(layer.type)) {
                return;
            }
            renderer.layerMap[layer.id] = layer;
            design.zones[layer.id]?.forEach((zone) => renderer.drawZone(zone, layer.id));
            design.tracks[layer.id]?.forEach((track) => {
                const color = manager.colorProvider.getColorForLayer(layer.id);
                const de = new DrawingElement(manager, track, 'tracks', ObjectSelectionType.Net);
                const graphic = renderer.renderTrack(track);
                // graphic.mask = renderer.maskMask;
                de.addGraphic(layer.id, graphic, color, renderer.withTypeProp(track, layer.id, renderer.netProperty(track.net)));
                manager.addElement(de);
            });
        });
        design.modules.forEach((module) => {
            const component = model.components[module.componentRef];
            if (!component) {
                throw new Error(`Could not find component ${module.componentRef} in project`);
            }
            const fp = component.footprints.find((f) => f.ref === module.footprintRef);
            if (!fp) {
                throw new Error(`Could not find footprint ${module.footprintRef} in component ${module.componentRef}`);
            }
            renderer.renderFootprint(module, fp, design.throughHoleLayers);
        });
        design.vias?.forEach((via) => renderer.drawVia(via));
        renderer.renderModelDrawings(design);
        manager.setDrawOverlayFunc(renderer.drawOverlay);
        renderer.renderMaskLayer(layers);
        manager.setVisibleLayers(Array.from(new Set([...visibleLayers, ...layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.Mask).map((l) => l.id)])).filter((l) => l !== 51));
        // manager.setVisibleLayers(layers.filter(l => l.type === Models.PcbLayerType.Mask).map(l => l.id));
        if (renderer.opts.drawBoundingBox) {
            renderer.drawBoundingBox(design);
        }
    }
    generateMaskLayer(layers, _model) {
        const maskLayers = layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.Mask && l.face === this.opts.face);
        if (maskLayers.length === 0) {
            this.manager.addRenderWarning('No mask layers found. Skipping drawering edges');
            return;
        }
        else if (maskLayers.length > 1) {
            this.manager.addRenderWarning('Multiple mask layers found. Using first found: ' + maskLayers[0].name);
        }
        this.maskMask = new TintedContainer();
        if (this.maskGraphic) {
            this.maskGraphic.zOrder = 5;
        }
        this.maskMask.zOrder = 10;
        // this.maskGraphic.mask = this.maskMask;
        // if (model.drawings[maskLayers[0].id]) {
        // 	for (const drawing of model.drawings[maskLayers[0].id]) {
        // 		switch (drawing.type) {
        // 			case 'rect':
        // 				basePoly.addFace(new Flatten.Box())
        // 		}
        // 	}
        // }
    }
    renderMaskLayer(layers) {
        const maskLayers = layers.filter((l) => l.type === distExports.DesignModels.PcbLayerType.Mask && l.face === this.opts.face);
        const faceLayers = layers.filter((l) => l.type === this.opts.face);
        if (maskLayers.length === 0) {
            this.manager.addRenderWarning('No mask layers found. Skipping drawering edges');
            return;
        }
        else if (maskLayers.length > 1) {
            this.manager.addRenderWarning('Multiple mask layers found. Using first found: ' + maskLayers[0].name);
        }
        if (faceLayers.length === 0) {
            this.manager.addRenderWarning('No face layers found. Skipping drawering edges');
            return;
        }
        else if (faceLayers.length > 1) {
            this.manager.addRenderWarning('Multiple face layers found. Using first found: ' + faceLayers[0].name);
        }
        const maskColor = this.manager.colorProvider.getColorForLayer(maskLayers[0].id);
        const faceColor = this.manager.colorProvider.getColorForLayer(faceLayers[0].id);
        const element = new DrawingElement(this.manager, {}, 'edges', ObjectSelectionType.None);
        if (this.maskGraphic) {
            element.addGraphic(maskLayers[0].id, this.maskGraphic, maskColor);
        }
        if (this.maskMask) {
            element.addGraphic(maskLayers[0].id, this.maskMask, faceColor);
        }
        this.manager.addElement(element);
    }
    renderEdges(layers, model) {
        const edgeLayers = layers.filter((l) => l.type === DesignModelsExports.PcbLayerType.Edge);
        if (edgeLayers.length === 0) {
            this.manager.addRenderWarning('No edge layers found. Skipping drawering edges');
            return;
        }
        else if (edgeLayers.length > 1) {
            this.manager.addRenderWarning('Multiple edge layers found. Using first found: ' + edgeLayers[0].name);
        }
        for (const edgeLayer of edgeLayers) {
            this.layerMap[edgeLayer.id] = edgeLayer;
        }
        // this.renderDrawings(model.edges, edgeLayers[0].id);
        const tc = new TrackConnector();
        const holes = [];
        const edges = edgeLayers
            .flatMap((edgeLayer) => model.drawings[edgeLayer.id])
            .concat(model.edges);
        for (const shape of edges) {
            switch (shape?.type) {
                case 'segment':
                    tc.addTrack({
                        ...shape,
                        start: TransformExports.scalePoint(shape.start, ConstantsExports.WORLD_SCALE),
                        end: TransformExports.scalePoint(shape.end, ConstantsExports.WORLD_SCALE),
                    });
                    break;
                case 'polygon': {
                    const polyPos = TransformExports.scalePoint(shape.pos || [0, 0], ConstantsExports.WORLD_SCALE);
                    tc.addPolygon({
                        ...shape,
                        points: shape.points.map((p) => TransformExports.translatePoint(TransformExports.scalePoint(p, ConstantsExports.WORLD_SCALE), polyPos)),
                    });
                    break;
                }
                case 'circle':
                    holes.push(shape);
                    break;
            }
        }
        // if (!tc.formsEnclosure()) {
        // 	// TODO probably don't want this?
        // 	throw new Error('TC did not find an enclosure');
        // }
        const tcPolys = tc.getPolygons();
        if (!tcPolys.length) {
            console.warn('TC did not generate any edge polygons');
            return;
        }
        const pixiPolys = tcPolys.map((tcPoly) => new PIXI.Polygon(tcPoly.vertices));
        const basePoly = pixiPolys.splice(0, 1)[0];
        const color = this.manager.colorProvider.getColorForLayer(edgeLayers[0].id);
        const edgeGraphic = new PIXI.Graphics();
        this.edgeGraphic = edgeGraphic;
        edgeGraphic.beginFill(ConstantsExports.COLOR_UNFILLED);
        edgeGraphic.drawPolygon(basePoly);
        edgeGraphic.endFill();
        edgeGraphic.beginHole();
        for (const poly of pixiPolys) {
            edgeGraphic.drawPolygon(poly);
        }
        for (const hole of holes) {
            edgeGraphic.drawCircle(hole.center[0] * ConstantsExports.WORLD_SCALE, hole.center[1] * ConstantsExports.WORLD_SCALE, hole.radius * ConstantsExports.WORLD_SCALE);
        }
        for (const via of model.vias) {
            edgeGraphic.drawCircle(via.center[0] * ConstantsExports.WORLD_SCALE, via.center[1] * ConstantsExports.WORLD_SCALE, (via.drillDiameter / 2) * ConstantsExports.WORLD_SCALE);
        }
        edgeGraphic.endHole();
        this.edgePoly = tc.getPoly();
        this.maskGraphic = edgeGraphic.clone();
        const element = new DrawingElement(this.manager, {}, 'edges', ObjectSelectionType.None);
        element.addGraphic(edgeLayers[0].id, edgeGraphic, color);
        this.manager.addElement(element);
    }
    renderModelDrawings(model) {
        for (const [layerId, drawings] of Object.entries(model.drawings)) {
            if (this.layerMap[layerId]) {
                this.renderDrawings(drawings, assertInteger(layerId));
            }
        }
    }
    renderFootprintDrawings(footprint, drawings, module, de, renderedProps, addProps) {
        // super.renderFootprintDrawings(
        // 	footprint,
        // 	drawings.map(d => {
        // 		return {
        // 			drawing: d.drawing,
        // 			layers: d.layers.filter(l => !!this.layerMap[this.mapFaceLayer(l, module.layer)])
        // 		}
        // 	}).filter(d => d.layers.length > 0),
        // 	module,
        // 	de,
        // 	renderedProps,
        // 	addProps,
        // );
        drawings.forEach((drawing) => {
            switch (drawing.layers.length) {
                case 0:
                    this.manager.addRenderWarning(`Footprint ${footprint.name} did not have drawing layers`);
                    return;
                case 1:
                    break;
                default:
                    this.manager.addRenderWarning(`Footprint ${footprint.name} had ${drawing.layers.length} drawing layers, expected 1`);
                    return;
            }
            const layer = this.mapFaceLayer(drawing.layers[0], module.layer);
            if (!this.layerMap[layer]) {
                return;
            }
            if (this.layerMap[layer].type === distExports.DesignModels.PcbLayerType.Mask ||
                this.layerMap[layer].type === distExports.DesignModels.PcbLayerType.Edge) {
                switch (drawing.drawing.type) {
                    case 'segment': {
                        this.renderFootprintSegment(drawing.drawing, module, footprint, layer, de, undefined, addProps);
                        break;
                    }
                    case 'track': {
                        this.renderFootprintTrack(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'rect': {
                        this.renderFootprintRectangle(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'text': {
                        if (drawing.drawing.ref_designator && drawing.drawing.ref && module.drawProperties[drawing.drawing.ref]) {
                            renderedProps.add(drawing.drawing.ref);
                        }
                        this.renderFootprintText(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'circle': {
                        this.renderCircleHole(drawing.drawing, module, this.edgeGraphic);
                        // this.maskMask.addChild(g);
                        break;
                    }
                    case 'arc': {
                        this.renderFootprintArc(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'curve': {
                        this.renderFootprintCurve(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'polygon': {
                        this.renderFootprintPolygon(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    default:
                        this.manager.addRenderWarning(`Footprint ${footprint.name} uses unsupported drawing type ${drawing.drawing.type}`);
                }
            }
            else {
                switch (drawing.drawing.type) {
                    case 'segment': {
                        this.renderFootprintSegment(drawing.drawing, module, footprint, layer, de, undefined, addProps);
                        break;
                    }
                    case 'track': {
                        this.renderFootprintTrack(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'rect': {
                        this.renderFootprintRectangle(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'text': {
                        if (drawing.drawing.ref_designator && drawing.drawing.ref && module.drawProperties[drawing.drawing.ref]) {
                            renderedProps.add(drawing.drawing.ref);
                        }
                        this.renderFootprintText(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'circle': {
                        this.renderFootprintCircle(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'arc': {
                        this.renderFootprintArc(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'curve': {
                        this.renderFootprintCurve(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    case 'polygon': {
                        this.renderFootprintPolygon(drawing.drawing, module, footprint, layer, de, addProps);
                        break;
                    }
                    default:
                        this.manager.addRenderWarning(`Footprint ${footprint.name} uses unsupported drawing type ${drawing.drawing.type}`);
                }
            }
        });
    }
    renderCircleHole(circle, module, g) {
        g.beginFill(ConstantsExports.COLOR_UNFILLED);
        g.beginHole();
        const scaleFactor = ConstantsExports.WORLD_SCALE;
        const center = TransformExports.translatePoint(TransformExports.rotatePoint([0, 0], -(module?.transform.angle || 0), module?.transform.pos || [0, 0]), module?.center || [0, 0]);
        const circleCenter = TransformExports.translatePoint(TransformExports.rotatePoint(circle.center, -(module?.transform.angle || 0)), center);
        g.drawCircle(scaleFactor * circleCenter[0], scaleFactor * circleCenter[1], scaleFactor * max(circle.radius, ConstantsExports.MIN_CIRCLE_RADIUS));
        g.endHole();
        g.endFill();
    }
    renderDrawings(drawings, defaultLayer, tags) {
        if (typeof defaultLayer !== 'number') {
            this.manager.addRenderWarning('Missing layer while rendering drawings');
            return [];
        }
        if (!drawings) {
            this.manager.addRenderWarning(`Empty drawings object for layer ${defaultLayer}`);
            return [];
        }
        const elements = [];
        const layerInfo = this.layerMap[defaultLayer];
        if (!layerInfo) {
            return [];
        }
        // const maskLayer = Object.values(this.layerMap).filter(
        // 	(l) => l.face === this.opts.face && l.type === Models.PcbLayerType.Mask,
        // );
        drawings.forEach((item) => {
            const net = this.netProperty(item.net);
            const de = new DrawingElement(this.manager, item, 'drawings', net ? ObjectSelectionType.Net : ObjectSelectionType.None);
            let deUsed = false;
            if (item.type === 'zone') {
                // Zones are special!
                deUsed = this.renderZone(defaultLayer, item, de, tags);
            }
            else {
                const gs = this.renderDrawingElement(item, tags, defaultLayer);
                for (const { g, layer } of gs) {
                    const color = this.manager.colorProvider.getColorForLayer(layer);
                    de.addGraphic(layer, g, color, this.withTypeProp(item, layer, {
                        tags,
                        properties: net?.properties,
                    }));
                    if (item.type === 'text') {
                        this.addTextBoundsToDrawingEelment(de, layer, g, color, net?.properties);
                    }
                    deUsed = true;
                    if (layerInfo.type === distExports.DesignModels.PcbLayerType.Mask) {
                        if (g instanceof PIXI.Graphics) {
                            const maskG = g.clone();
                            this.maskMask.addChild(maskG);
                            // de.addGraphic(maskLayer[0].id, maskG);
                        }
                    }
                }
            }
            if (deUsed) {
                this.manager.addElement(de);
                elements.push(de);
            }
        });
        return elements;
    }
    renderPad(module, _footprint, pad, de, throughHoleLayers) {
        let scaleFactor = ConstantsExports.WORLD_SCALE;
        let graphicScale = 1;
        if (pad.padType === 'smd' && module.layer !== this.opts.face) {
            return;
        }
        if (pad.shape === 'circle' || pad.shape === 'oval' || pad.shape === 'roundrect') {
            scaleFactor = ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
            graphicScale = ConstantsExports.WORLD_SCALE / ConstantsExports.TEXTURIZED_ELEMENT_SCALE;
        }
        const diameter = [
            scaleFactor * max(pad.diameter[0], ConstantsExports.SEGMENT_MIN_WIDTH),
            scaleFactor * max(pad.diameter[1], ConstantsExports.SEGMENT_MIN_WIDTH),
        ];
        const rotated = TransformExports.getXyWithMirror(TransformExports.rotatePoint(pad.pos, -module.transform.angle, module.transform.pos), module.transform.mirrored);
        const translate = [rotated[0] + module.center[0], rotated[1] + module.center[1]];
        let hitArea;
        for (let layer of pad.layers || throughHoleLayers) {
            layer = this.mapFaceLayer(layer, module.layer);
            const layerInfo = this.layerMap[layer];
            if (!layerInfo) {
                continue;
            }
            const color = this.manager.colorProvider.getColorForLayer(layer);
            const g = new PIXI.Graphics();
            const angle = TransformExports.clampRotation(-pad.angle - module.transform.angle);
            g.angle = angle;
            g.position.x = ConstantsExports.WORLD_SCALE * translate[0];
            g.position.y = ConstantsExports.WORLD_SCALE * translate[1];
            g.scale.set(graphicScale, graphicScale);
            if (pad.shape === 'circle' || pad.shape === 'oval') {
                // if (layerInfo.type === Models.PcbLayerType.Mask) {
                // 	this.maskGraphic.beginHole();
                // 	this.maskGraphic.beginFill(COLOR_UNFILLED);
                // 	this.maskGraphic.drawEllipse(0, 0, diameter[0] / 2, diameter[1] / 2);
                // 	this.maskGraphic.endFill();
                // 	this.maskGraphic.endHole();
                // } else {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.drawEllipse(0, 0, diameter[0] / 2, diameter[1] / 2);
                g.endFill();
                hitArea = new PIXI.Ellipse(0, 0, diameter[0] / 2, diameter[1] / 2);
                // }
            }
            else if (pad.shape === 'rect' ||
                (pad.shape === 'roundrect' && (!pad.radius || pad.radius < ConstantsExports.MIN_ROUND_RECT_RADIUS))) {
                // if (layerInfo.type === Models.PcbLayerType.Mask) {
                // 	this.maskGraphic.beginHole();
                // 	this.maskGraphic.beginFill(COLOR_UNFILLED);
                // 	this.maskGraphic.drawRect(
                // 		- diameter[0] / 2,
                // 		- diameter[1] / 2,
                // 		diameter[0],
                // 		diameter[1]
                // 	);
                // 	this.maskGraphic.endFill();
                // 	this.maskGraphic.endHole();
                // } else {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.drawRect(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1]);
                g.endFill();
                hitArea = new PIXI.Rectangle(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1]);
                // }
            }
            else if (pad.shape === 'roundrect') {
                // if (layerInfo.type === Models.PcbLayerType.Mask) {
                // 	this.maskGraphic.beginHole();
                // 	this.maskGraphic.beginFill(COLOR_UNFILLED);
                // 	this.maskGraphic.drawRect(
                // 		- diameter[0] / 2,
                // 		- diameter[1] / 2,
                // 		diameter[0],
                // 		diameter[1]
                // 	);
                // 	this.maskGraphic.endFill();
                // 	this.maskGraphic.endHole();
                // } else {
                g.beginFill(ConstantsExports.COLOR_UNFILLED);
                g.drawRoundedRect(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1], ConstantsExports.WORLD_SCALE * max(pad.radius, ConstantsExports.MIN_ROUND_RECT_RADIUS));
                g.endFill();
                hitArea = new PIXI.Rectangle(-diameter[0] / 2, -diameter[1] / 2, diameter[0], diameter[1]);
                // }
            }
            else if (pad.shape === 'custom') {
                if (!pad.polygons || pad.polygons.length === 0) {
                    this.manager.addRenderWarning(`Polygons prop was expected for '${pad.shape}'.`);
                    continue;
                }
                pad.polygons.forEach((path) => {
                    // if (layerInfo.type === Models.PcbLayerType.Mask) {
                    // 	this.maskGraphic.beginHole();
                    // 	this.maskGraphic.beginFill(COLOR_UNFILLED);
                    // 	this.maskGraphic.moveTo(WORLD_SCALE * path[0][0], WORLD_SCALE * path[0][1]);
                    // 	for (let i=1; i < path.length; i++) {
                    // 		this.maskGraphic.lineTo(WORLD_SCALE * path[i][0], WORLD_SCALE * path[i][1]);
                    // 	}
                    // 	this.maskGraphic.endFill();
                    // 	this.maskGraphic.endHole();
                    // } else {
                    g.beginFill(ConstantsExports.COLOR_UNFILLED);
                    g.moveTo(ConstantsExports.WORLD_SCALE * path[0][0], ConstantsExports.WORLD_SCALE * path[0][1]);
                    for (let i = 1; i < path.length; i++) {
                        g.lineTo(ConstantsExports.WORLD_SCALE * path[i][0], ConstantsExports.WORLD_SCALE * path[i][1]);
                    }
                    g.endFill();
                    hitArea = new PIXI.Polygon(path.map(([x, y]) => new PIXI.Point(ConstantsExports.WORLD_SCALE * x, ConstantsExports.WORLD_SCALE * y)));
                    // }
                });
                if (pad.polygons.length > 1) {
                    this.manager.addRenderWarning(`Pads with multiple polygons not supported correctly.`);
                }
            }
            else {
                // todo
                this.manager.addRenderWarning(`Drawing of pad shape '${pad.shape}' has not been implemented.`);
                continue;
            }
            if (layerInfo.type === distExports.DesignModels.PcbLayerType.Mask) {
                this.maskMask.addChild(g);
                // de.addGraphic(layer, g, color);
                continue;
            }
            if (pad.drillDiameter) {
                g.beginHole();
                g.drawEllipse(0, 0, (scaleFactor * max(pad.drillDiameter[0], 0)) / 2, (scaleFactor * max(pad.drillDiameter[1], 0)) / 2);
                g.endHole();
            }
            if (!this.disableHitboxes) {
                g.hitArea = hitArea || g.getBounds();
            }
            de.addGraphic(layer, g, color, this.withTypeProp(pad, layer, this.netProperty(module.pinNets[pad.ref])));
            // ## Text calculations
            // Start with an initial font and get the measurements.
            // Scale the font to fit inside the area (minus 10% padding).
            // If area height > width, then rotate it 90. Otherwise renders at 0.
            if (this.opts.generatePadText) {
                const net = module.pinNets[pad.ref]?.split('/').at(-1);
                const resolvedNet = net ? this.resolvePinNameForPadText(net) : undefined;
                const netText = resolvedNet?.text ?? '';
                let textValue = '';
                let netOffset = 0;
                if (Boolean(pad.name) && Boolean(netText)) {
                    textValue = `${pad.name}\n${netText}`;
                    netOffset = pad.name.length + 1;
                }
                else if (pad.name) {
                    textValue = pad.name;
                }
                else {
                    textValue = netText;
                }
                if (!textValue) {
                    continue;
                }
                const diameters = [...pad.diameter];
                const bWidth = Math.max(...diameters);
                const bHeight = Math.min(...diameters.map((v) => Math.abs(v)).filter((d) => d > 0));
                let rotateText = pad.diameter[0] < pad.diameter[1];
                if (bWidth > bHeight && ((angle > 45 && angle < 135) || (angle > 225 && angle < 315))) {
                    rotateText = !rotateText;
                }
                const text = this.renderText({
                    type: 'text',
                    justify: [0, 0],
                    pos: translate,
                    size: Math.min(bWidth / textValue.length, bHeight),
                    thickness: 1,
                    value: textValue,
                    textTransforms: resolvedNet?.transforms.map((transform) => transform.type === 'bar' ?
                        { ...transform, from: transform.from + netOffset, to: transform.to + netOffset }
                        : transform),
                    angle: rotateText ? -90 : 0,
                });
                if (text) {
                    const color = this.manager.colorProvider.getColorForTool('pad-labels');
                    de.addGraphic(layer, text, color, {
                        tags: [{ tag: ConstantsExports.TAG_PAD_LABELS, defaultVisible: false }],
                    });
                }
            }
        }
    }
}

export { Color, CommentOverlay, Constants$1 as Constants, DefaultLayerColorProvider, DrawingColorizationMode, DrawingElement, DrawingManager, DrawingProperties, InteractionState, InteractiveRulerTool, LinkHandlingMode, ModelPreviewRenderer, ModelRenderer, ObjectSelectionType, ProbeTool, RenderBoardToManager, RenderDrawingToManager, RenderSchematicToManager, SelectionMode, SelectionPoint, SelectionRectangle, colorToGraphicsOpts, colorToGraphicsOpts2, colorToGraphicsOpts3, constrainRect, expandRect, getContrastingColor, getContrastingGraphicsOpts, graphicsOverlayDrawing, parseColorString, parsePinPropertyKey, pinPropertyKey };
//# sourceMappingURL=index.es.mjs.map
